// <auto-generated/>
// Contents of: hl7.fhir.r4.core version: 4.0.1
// Primitive Naming Style: None
// Complex Type / Resource Naming Style: PascalCase
// Interaction Naming Style: None
// Extension Support: NonPrimitives
/**
 * An address expressed using postal conventions (as opposed to GPS or other location definition formats).  This data type may be used to convey addresses for use in delivering mail as well as for visiting locations which might not be valid for mail delivery.  There are a variety of postal address formats defined around the world.
 */
export interface Address extends Element {
  /**
   * The name of the city, town, suburb, village or other community or delivery center.
   */
  city?: string;
  _city?: Element;
  /**
   * ISO 3166 3 letter codes can be used in place of a human readable country name.
   */
  country?: string;
  _country?: Element;
  /**
   * District is sometimes known as county, but in some regions 'county' is used in place of city (municipality), so county name should be conveyed in city instead.
   */
  district?: string;
  _district?: Element;
  /**
   * This component contains the house number, apartment number, street name, street direction,  P.O. Box number, delivery hints, and similar address information.
   */
  line?: string[];
  _line?: Element;
  /**
   * Time period when address was/is in use.
   */
  period?: Period;
  /**
   * A postal code designating a region defined by the postal service.
   */
  postalCode?: string;
  _postalCode?: Element;
  /**
   * Sub-unit of a country with limited sovereignty in a federally organized country. A code may be used if codes are in common use (e.g. US 2 letter state codes).
   */
  state?: string;
  _state?: Element;
  /**
   * Can provide both a text representation and parts. Applications updating an address SHALL ensure that  when both text and parts are present,  no content is included in the text that isn't found in a part.
   */
  text?: string;
  _text?: Element;
  /**
   * The definition of Address states that "address is intended to describe postal addresses, not physical locations". However, many applications track whether an address has a dual purpose of being a location that can be visited as well as being a valid delivery destination, and Postal addresses are often used as proxies for physical locations (also see the [Location](location.html#) resource).
   */
  type?: string;
  _type?: Element;
  /**
   * Applications can assume that an address is current unless it explicitly says that it is temporary or old.
   */
  use?: string;
  _use?: Element;
}
/**
 * Code Values for the Address.type field
 */
export enum AddressTypeCodes {
  POSTAL = 'postal',
  PHYSICAL = 'physical',
  BOTH = 'both',
}
/**
 * Code Values for the Address.use field
 */
export enum AddressUseCodes {
  HOME = 'home',
  WORK = 'work',
  TEMP = 'temp',
  OLD = 'old',
  BILLING = 'billing',
}
/**
 * A duration of time during which an organism (or a process) has existed.
 */
export interface Age extends Quantity {}
/**
 * A  text note which also  contains information about who made the statement and when.
 */
export interface Annotation extends Element {
  /**
   * Organization is used when there's no need for specific attribution as to who made the comment.
   */
  authorReference?: Reference;
  /**
   * Organization is used when there's no need for specific attribution as to who made the comment.
   */
  authorString?: string;
  _authorString?: Element;
  /**
   * The text of the annotation in markdown format.
   */
  text: string;
  _text?: Element;
  /**
   * Indicates when this particular annotation was made.
   */
  time?: string;
  _time?: Element;
}
/**
 * For referring to data content defined in other formats.
 */
export interface Attachment extends Element {
  /**
   * Identifies the type of the data in the attachment and allows a method to be chosen to interpret or render the data. Includes mime type parameters such as charset where appropriate.
   */
  contentType?: string;
  _contentType?: Element;
  /**
   * The date that the attachment was first created.
   */
  creation?: string;
  _creation?: Element;
  /**
   * The base64-encoded data SHALL be expressed in the same character set as the base resource XML or JSON.
   */
  data?: string;
  _data?: Element;
  /**
   * The hash is calculated on the data prior to base64 encoding, if the data is based64 encoded. The hash is not intended to support digital signatures. Where protection against malicious threats a digital signature should be considered, see [Provenance.signature](provenance-definitions.html#Provenance.signature) for mechanism to protect a resource with a digital signature.
   */
  hash?: string;
  _hash?: Element;
  /**
   * The human language of the content. The value can be any valid value according to BCP 47.
   */
  language?: string;
  _language?: Element;
  /**
   * The number of bytes is redundant if the data is provided as a base64binary, but is useful if the data is provided as a url reference.
   */
  size?: number;
  /**
   * A label or set of text to display in place of the data.
   */
  title?: string;
  _title?: Element;
  /**
   * If both data and url are provided, the url SHALL point to the same content as the data contains. Urls may be relative references or may reference transient locations such as a wrapping envelope using cid: though this has ramifications for using signatures. Relative URLs are interpreted relative to the service url, like a resource reference, rather than relative to the resource itself. If a URL is provided, it SHALL resolve to actual data.
   */
  url?: string;
  _url?: Element;
}
/**
 * Base definition for all elements that are defined inside a resource - but not those in a data type.
 */
export interface BackboneElement extends Element {
  /**
   * There can be no stigma associated with the use of extensions by any application, project, or standard - regardless of the institution or jurisdiction that uses or defines the extensions.  The use of extensions is what allows the FHIR specification to retain a core level of simplicity for everyone.
   */
  modifierExtension?: Extension[];
}
/**
 * A concept that may be defined by a formal reference to a terminology or ontology or may be provided by text.
 */
export interface CodeableConcept extends Element {
  /**
   * Codes may be defined very casually in enumerations, or code lists, up to very formal definitions such as SNOMED CT - see the HL7 v3 Core Principles for more information.  Ordering of codings is undefined and SHALL NOT be used to infer meaning. Generally, at most only one of the coding values will be labeled as UserSelected = true.
   */
  coding?: Coding[];
  /**
   * Very often the text is the same as a displayName of one of the codings.
   */
  text?: string;
  _text?: Element;
}
/**
 * A reference to a code defined by a terminology system.
 */
export interface Coding extends Element {
  /**
   * A symbol in syntax defined by the system. The symbol may be a predefined code or an expression in a syntax defined by the coding system (e.g. post-coordination).
   */
  code?: string;
  _code?: Element;
  /**
   * A representation of the meaning of the code in the system, following the rules of the system.
   */
  display?: string;
  _display?: Element;
  /**
   * The URI may be an OID (urn:oid:...) or a UUID (urn:uuid:...).  OIDs and UUIDs SHALL be references to the HL7 OID registry. Otherwise, the URI should come from HL7's list of FHIR defined special URIs or it should reference to some definition that establishes the system clearly and unambiguously.
   */
  system?: string;
  _system?: Element;
  /**
   * Amongst a set of alternatives, a directly chosen code is the most appropriate starting point for new translations. There is some ambiguity about what exactly 'directly chosen' implies, and trading partner agreement may be needed to clarify the use of this element and its consequences more completely.
   */
  userSelected?: boolean;
  _userSelected?: Element;
  /**
   * Where the terminology does not clearly define what string should be used to identify code system versions, the recommendation is to use the date (expressed in FHIR date format) on which that version was officially published as the version date.
   */
  version?: string;
  _version?: Element;
}
/**
 * Specifies contact information for a person or organization.
 */
export interface ContactDetail extends Element {
  /**
   * If there is no named individual, the telecom information is for the organization as a whole.
   */
  name?: string;
  _name?: Element;
  /**
   * The contact details for the individual (if a name was provided) or the organization.
   */
  telecom?: ContactPoint[];
}
/**
 * Details for all kinds of technology mediated contact points for a person or organization, including telephone, email, etc.
 */
export interface ContactPoint extends Element {
  /**
   * Time period when the contact point was/is in use.
   */
  period?: Period;
  /**
   * Note that rank does not necessarily follow the order in which the contacts are represented in the instance.
   */
  rank?: number;
  /**
   * Telecommunications form for contact point - what communications system is required to make use of the contact.
   */
  system?: string;
  _system?: Element;
  /**
   * Applications can assume that a contact is current unless it explicitly says that it is temporary or old.
   */
  use?: string;
  _use?: Element;
  /**
   * Additional text data such as phone extension numbers, or notes about use of the contact are sometimes included in the value.
   */
  value?: string;
  _value?: Element;
}
/**
 * Code Values for the ContactPoint.system field
 */
export enum ContactPointSystemCodes {
  PHONE = 'phone',
  FAX = 'fax',
  EMAIL = 'email',
  PAGER = 'pager',
  URL = 'url',
  SMS = 'sms',
  OTHER = 'other',
}
/**
 * Code Values for the ContactPoint.use field
 */
export enum ContactPointUseCodes {
  HOME = 'home',
  WORK = 'work',
  TEMP = 'temp',
  OLD = 'old',
  MOBILE = 'mobile',
}
/**
 * A contributor to the content of a knowledge asset, including authors, editors, reviewers, and endorsers.
 */
export interface Contributor extends Element {
  /**
   * Contact details to assist a user in finding and communicating with the contributor.
   */
  contact?: ContactDetail[];
  /**
   * The name of the individual or organization responsible for the contribution.
   */
  name: string;
  _name?: Element;
  /**
   * The type of contributor.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the Contributor.type field
 */
export enum ContributorTypeCodes {
  AUTHOR = 'author',
  EDITOR = 'editor',
  REVIEWER = 'reviewer',
  ENDORSER = 'endorser',
}
/**
 * A measured amount (or an amount that can potentially be measured). Note that measured amounts include amounts that are not precisely quantified, including amounts involving arbitrary units and floating currencies.
 */
export interface Count extends Quantity {}
/**
 * Code filters specify additional constraints on the data, specifying the value set of interest for a particular element of the data. Each code filter defines an additional constraint on the data, i.e. code filters are AND'ed, not OR'ed.
 */
export interface DataRequirementCodeFilter extends Element {
  /**
   * The codes for the code filter. If values are given, the filter will return only those data items for which the code-valued attribute specified by the path has a value that is one of the specified codes. If codes are specified in addition to a value set, the filter returns items matching a code in the value set or one of the specified codes.
   */
  code?: Coding[];
  /**
   * The path attribute contains a [Simple FHIRPath Subset](fhirpath.html#simple) that allows path traversal, but not calculation.
   */
  path?: string;
  _path?: Element;
  /**
   * A token parameter that refers to a search parameter defined on the specified type of the DataRequirement, and which searches on elements of type code, Coding, or CodeableConcept.
   */
  searchParam?: string;
  _searchParam?: Element;
  /**
   * The valueset for the code filter. The valueSet and code elements are additive. If valueSet is specified, the filter will return only those data items for which the value of the code-valued element specified in the path is a member of the specified valueset.
   */
  valueSet?: string;
  _valueSet?: Element;
}
/**
 * Date filters specify additional constraints on the data in terms of the applicable date range for specific elements. Each date filter specifies an additional constraint on the data, i.e. date filters are AND'ed, not OR'ed.
 */
export interface DataRequirementDateFilter extends Element {
  /**
   * The path attribute contains a [Simple FHIR Subset](fhirpath.html#simple) that allows path traversal, but not calculation.
   */
  path?: string;
  _path?: Element;
  /**
   * A date parameter that refers to a search parameter defined on the specified type of the DataRequirement, and which searches on elements of type date, dateTime, Period, Schedule, or Timing.
   */
  searchParam?: string;
  _searchParam?: Element;
  /**
   * The value of the filter. If period is specified, the filter will return only those data items that fall within the bounds determined by the Period, inclusive of the period boundaries. If dateTime is specified, the filter will return only those data items that are equal to the specified dateTime. If a Duration is specified, the filter will return only those data items that fall within Duration before now.
   */
  valueDateTime?: string;
  _valueDateTime?: Element;
  /**
   * The value of the filter. If period is specified, the filter will return only those data items that fall within the bounds determined by the Period, inclusive of the period boundaries. If dateTime is specified, the filter will return only those data items that are equal to the specified dateTime. If a Duration is specified, the filter will return only those data items that fall within Duration before now.
   */
  valuePeriod?: Period;
  /**
   * The value of the filter. If period is specified, the filter will return only those data items that fall within the bounds determined by the Period, inclusive of the period boundaries. If dateTime is specified, the filter will return only those data items that are equal to the specified dateTime. If a Duration is specified, the filter will return only those data items that fall within Duration before now.
   */
  valueDuration?: Duration;
}
/**
 * This element can be used in combination with the sort element to specify quota requirements such as "the most recent 5" or "the highest 5". When multiple sorts are specified, they are applied in the order they appear in the resource.
 */
export interface DataRequirementSort extends Element {
  /**
   * The direction of the sort, ascending or descending.
   */
  direction: string;
  _direction?: Element;
  /**
   * The attribute of the sort. The specified path must be resolvable from the type of the required data. The path is allowed to contain qualifiers (.) to traverse sub-elements, as well as indexers ([x]) to traverse multiple-cardinality sub-elements. Note that the index must be an integer constant.
   */
  path: string;
  _path?: Element;
}
/**
 * Code Values for the DataRequirement.sort.direction field
 */
export enum DataRequirementSortDirectionCodes {
  ASCENDING = 'ascending',
  DESCENDING = 'descending',
}
/**
 * Describes a required data item for evaluation in terms of the type of data, and optional code or date-based filters of the data.
 */
export interface DataRequirement extends Element {
  /**
   * Code filters specify additional constraints on the data, specifying the value set of interest for a particular element of the data. Each code filter defines an additional constraint on the data, i.e. code filters are AND'ed, not OR'ed.
   */
  codeFilter?: DataRequirementCodeFilter[];
  /**
   * Date filters specify additional constraints on the data in terms of the applicable date range for specific elements. Each date filter specifies an additional constraint on the data, i.e. date filters are AND'ed, not OR'ed.
   */
  dateFilter?: DataRequirementDateFilter[];
  /**
   * This element can be used in combination with the sort element to specify quota requirements such as "the most recent 5" or "the highest 5".
   */
  limit?: number;
  /**
   * Indicates that specific elements of the type are referenced by the knowledge module and must be supported by the consumer in order to obtain an effective evaluation. This does not mean that a value is required for this element, only that the consuming system must understand the element and be able to provide values for it if they are available.
   * The value of mustSupport SHALL be a FHIRPath resolveable on the type of the DataRequirement. The path SHALL consist only of identifiers, constant indexers, and .resolve() (see the [Simple FHIRPath Profile](fhirpath.html#simple) for full details).
   */
  mustSupport?: string[];
  _mustSupport?: Element;
  /**
   * The profile of the required data, specified as the uri of the profile definition.
   */
  profile?: string[];
  _profile?: Element;
  /**
   * This element can be used in combination with the sort element to specify quota requirements such as "the most recent 5" or "the highest 5". When multiple sorts are specified, they are applied in the order they appear in the resource.
   */
  sort?: DataRequirementSort[];
  /**
   * The subject of a data requirement is critical, as the data being specified is determined with respect to a particular subject. This corresponds roughly to the notion of a Compartment in that it limits what data is available based on its relationship to the subject. In CQL, this corresponds to the context declaration.
   */
  subjectCodeableConcept?: CodeableConcept;
  /**
   * The subject of a data requirement is critical, as the data being specified is determined with respect to a particular subject. This corresponds roughly to the notion of a Compartment in that it limits what data is available based on its relationship to the subject. In CQL, this corresponds to the context declaration.
   */
  subjectReference?: Reference;
  /**
   * The type of the required data, specified as the type name of a resource. For profiles, this value is set to the type of the base resource of the profile.
   */
  type: string;
  _type?: Element;
}
/**
 * A length - a value with a unit that is a physical distance.
 */
export interface Distance extends Quantity {}
/**
 * The amount of medication administered.
 */
export interface DosageDoseAndRate extends Element {
  /**
   * Note that this specifies the quantity of the specified medication, not the quantity for each active ingredient(s). Each ingredient amount can be communicated in the Medication resource. For example, if one wants to communicate that a tablet was 375 mg, where the dose was one tablet, you can use the Medication resource to document that the tablet was comprised of 375 mg of drug XYZ. Alternatively if the dose was 375 mg, then you may only need to use the Medication resource to indicate this was a tablet. If the example were an IV such as dopamine and you wanted to communicate that 400mg of dopamine was mixed in 500 ml of some IV solution, then this would all be communicated in the Medication resource. If the administration is not intended to be instantaneous (rate is present or timing has a duration), this can be specified to convey the total amount to be administered over the period of time as indicated by the schedule e.g. 500 ml in dose, with timing used to convey that this should be done over 4 hours.
   */
  doseRange?: Range;
  /**
   * Note that this specifies the quantity of the specified medication, not the quantity for each active ingredient(s). Each ingredient amount can be communicated in the Medication resource. For example, if one wants to communicate that a tablet was 375 mg, where the dose was one tablet, you can use the Medication resource to document that the tablet was comprised of 375 mg of drug XYZ. Alternatively if the dose was 375 mg, then you may only need to use the Medication resource to indicate this was a tablet. If the example were an IV such as dopamine and you wanted to communicate that 400mg of dopamine was mixed in 500 ml of some IV solution, then this would all be communicated in the Medication resource. If the administration is not intended to be instantaneous (rate is present or timing has a duration), this can be specified to convey the total amount to be administered over the period of time as indicated by the schedule e.g. 500 ml in dose, with timing used to convey that this should be done over 4 hours.
   */
  doseQuantity?: Quantity;
  /**
   * It is possible to supply both a rate and a doseQuantity to provide full details about how the medication is to be administered and supplied. If the rate is intended to change over time, depending on local rules/regulations, each change should be captured as a new version of the MedicationRequest with an updated rate, or captured with a new MedicationRequest with the new rate.
   * It is possible to specify a rate over time (for example, 100 ml/hour) using either the rateRatio and rateQuantity.  The rateQuantity approach requires systems to have the capability to parse UCUM grammer where ml/hour is included rather than a specific ratio where the time is specified as the denominator.  Where a rate such as 500ml over 2 hours is specified, the use of rateRatio may be more semantically correct than specifying using a rateQuantity of 250 mg/hour.
   */
  rateRatio?: Ratio;
  /**
   * It is possible to supply both a rate and a doseQuantity to provide full details about how the medication is to be administered and supplied. If the rate is intended to change over time, depending on local rules/regulations, each change should be captured as a new version of the MedicationRequest with an updated rate, or captured with a new MedicationRequest with the new rate.
   * It is possible to specify a rate over time (for example, 100 ml/hour) using either the rateRatio and rateQuantity.  The rateQuantity approach requires systems to have the capability to parse UCUM grammer where ml/hour is included rather than a specific ratio where the time is specified as the denominator.  Where a rate such as 500ml over 2 hours is specified, the use of rateRatio may be more semantically correct than specifying using a rateQuantity of 250 mg/hour.
   */
  rateRange?: Range;
  /**
   * It is possible to supply both a rate and a doseQuantity to provide full details about how the medication is to be administered and supplied. If the rate is intended to change over time, depending on local rules/regulations, each change should be captured as a new version of the MedicationRequest with an updated rate, or captured with a new MedicationRequest with the new rate.
   * It is possible to specify a rate over time (for example, 100 ml/hour) using either the rateRatio and rateQuantity.  The rateQuantity approach requires systems to have the capability to parse UCUM grammer where ml/hour is included rather than a specific ratio where the time is specified as the denominator.  Where a rate such as 500ml over 2 hours is specified, the use of rateRatio may be more semantically correct than specifying using a rateQuantity of 250 mg/hour.
   */
  rateQuantity?: Quantity;
  /**
   * The kind of dose or rate specified, for example, ordered or calculated.
   */
  type?: CodeableConcept;
}
/**
 * Indicates how the medication is/was taken or should be taken by the patient.
 */
export interface Dosage extends BackboneElement {
  /**
   * Information about administration or preparation of the medication (e.g. "infuse as rapidly as possibly via intraperitoneal port" or "immediately following drug x") should be populated in dosage.text.
   */
  additionalInstruction?: CodeableConcept[];
  /**
   * Can express "as needed" without a reason by setting the Boolean = True.  In this case the CodeableConcept is not populated.  Or you can express "as needed" with a reason by including the CodeableConcept.  In this case the Boolean is assumed to be True.  If you set the Boolean to False, then the dose is given according to the schedule and is not "prn" or "as needed".
   */
  asNeededBoolean?: boolean;
  _asNeededBoolean?: Element;
  /**
   * Can express "as needed" without a reason by setting the Boolean = True.  In this case the CodeableConcept is not populated.  Or you can express "as needed" with a reason by including the CodeableConcept.  In this case the Boolean is assumed to be True.  If you set the Boolean to False, then the dose is given according to the schedule and is not "prn" or "as needed".
   */
  asNeededCodeableConcept?: CodeableConcept;
  /**
   * The amount of medication administered.
   */
  doseAndRate?: DosageDoseAndRate[];
  /**
   * This is intended for use as an adjunct to the dosage when there is an upper cap.  For example, a body surface area related dose with a maximum amount, such as 1.5 mg/m2 (maximum 2 mg) IV over 5 – 10 minutes would have doseQuantity of 1.5 mg/m2 and maxDosePerAdministration of 2 mg.
   */
  maxDosePerAdministration?: Quantity;
  /**
   * Upper limit on medication per lifetime of the patient.
   */
  maxDosePerLifetime?: Quantity;
  /**
   * This is intended for use as an adjunct to the dosage when there is an upper cap.  For example "2 tablets every 4 hours to a maximum of 8/day".
   */
  maxDosePerPeriod?: Ratio;
  /**
   * Terminologies used often pre-coordinate this term with the route and or form of administration.
   */
  method?: CodeableConcept;
  /**
   * Instructions in terms that are understood by the patient or consumer.
   */
  patientInstruction?: string;
  _patientInstruction?: Element;
  /**
   * How drug should enter body.
   */
  route?: CodeableConcept;
  /**
   * Indicates the order in which the dosage instructions should be applied or interpreted.
   */
  sequence?: number;
  /**
   * If the use case requires attributes from the BodySite resource (e.g. to identify and track separately) then use the standard extension [bodySite](extension-bodysite.html).  May be a summary code, or a reference to a very precise definition of the location, or both.
   */
  site?: CodeableConcept;
  /**
   * Free text dosage instructions e.g. SIG.
   */
  text?: string;
  _text?: Element;
  /**
   * This attribute might not always be populated while the Dosage.text is expected to be populated.  If both are populated, then the Dosage.text should reflect the content of the Dosage.timing.
   */
  timing?: Timing;
}
/**
 * A length of time.
 */
export interface Duration extends Quantity {}
/**
 * Base definition for all elements in a resource.
 */
export interface Element {
  /**
   * There can be no stigma associated with the use of extensions by any application, project, or standard - regardless of the institution or jurisdiction that uses or defines the extensions.  The use of extensions is what allows the FHIR specification to retain a core level of simplicity for everyone.
   */
  extension?: Extension[];
  /**
   * Unique id for the element within a resource (for internal references). This may be any string value that does not contain spaces.
   */
  id?: string;
  _id?: Element;
}
/**
 * If there is no discriminator, the content is hard to process, so this should be avoided.
 */
export interface ElementDefinitionSlicingDiscriminator extends Element {
  /**
   * The only FHIRPath functions that are allowed are as(type), resolve(), and extension(url).
   */
  path: string;
  _path?: Element;
  /**
   * How the element value is interpreted when discrimination is evaluated.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the ElementDefinition.slicing.discriminator.type field
 */
export enum ElementDefinitionSlicingDiscriminatorTypeCodes {
  VALUE = 'value',
  EXISTS = 'exists',
  PATTERN = 'pattern',
  TYPE = 'type',
  PROFILE = 'profile',
}
/**
 * The first element in the sequence, the one that carries the slicing, is the definition that applies to all the slices. This is based on the unconstrained element, but can apply any constraints as appropriate. This may include the common constraints on the children of the element.
 */
export interface ElementDefinitionSlicing extends Element {
  /**
   * If it's really not possible to differentiate them, the design should be re-evaluated to make the content usable.
   */
  description?: string;
  _description?: Element;
  /**
   * If there is no discriminator, the content is hard to process, so this should be avoided.
   */
  discriminator?: ElementDefinitionSlicingDiscriminator[];
  /**
   * Order should only be required when it is a pressing concern for presentation. Profile authors should consider making the order a feature of the rules about the narrative, not the rules about the data - requiring ordered data makes the profile much less re-usable.
   */
  ordered?: boolean;
  _ordered?: Element;
  /**
   * Allowing additional elements makes for a much for flexible template - it's open for use in wider contexts, but also means that the content of the resource is not closed, and applications have to decide how to handle content not described by the profile.
   */
  rules: string;
  _rules?: Element;
}
/**
 * Code Values for the ElementDefinition.slicing.rules field
 */
export enum ElementDefinitionSlicingRulesCodes {
  CLOSED = 'closed',
  OPEN = 'open',
  OPENATEND = 'openAtEnd',
}
/**
 * The base information does not carry any information that could not be determined from the path and related profiles, but making this determination requires both that the related profiles are available, and that the algorithm to determine them be available. For tooling simplicity, the base information must always be populated in element definitions in snap shots, even if it is the same.
 */
export interface ElementDefinitionBase extends Element {
  /**
   * This is provided to code generation, since the serialization representation in JSON differs depending on whether the base element has max > 1. Also, some forms of code generation may differ.
   */
  max: string;
  _max?: Element;
  /**
   * This is provided for consistency with max, and may affect code generation of mandatory elements of the base resource are generated differently (some reference implementations have done this).
   */
  min: number;
  /**
   * The Path that identifies the base element - this matches the ElementDefinition.path for that element. Across FHIR, there is only one base definition of any element - that is, an element definition on a [StructureDefinition](structuredefinition.html#) without a StructureDefinition.base.
   */
  path: string;
  _path?: Element;
}
/**
 * The Type of the element can be left blank in a differential constraint, in which case the type is inherited from the resource. Abstract types are not permitted to appear as a type when multiple types are listed.  (I.e. Abstract types cannot be part of a choice).
 */
export interface ElementDefinitionType extends Element {
  /**
   * See [Aggregation Rules](elementdefinition.html#aggregation) for further clarification.
   */
  aggregation?: string[];
  _aggregation?: Element;
  /**
   * If the element is a reference to another resource, this element contains "Reference", and the targetProfile element defines what resources can be referenced. The targetProfile may be a reference to the general definition of a resource (e.g. http://hl7.org/fhir/StructureDefinition/Patient).
   */
  code: string;
  _code?: Element;
  /**
   * It is possible to profile  backbone element (e.g. part of a resource), using the [profile-element](extension-elementdefinition-profile-element.html) extension.
   */
  profile?: string[];
  _profile?: Element;
  /**
   * Used when the type is "Reference" or "canonical", and identifies a profile structure or implementation Guide that applies to the target of the reference this element refers to. If any profiles are specified, then the content must conform to at least one of them. The URL can be a local reference - to a contained StructureDefinition, or a reference to another StructureDefinition or Implementation Guide by a canonical URL. When an implementation guide is specified, the target resource SHALL conform to at least one profile defined in the implementation guide.
   */
  targetProfile?: string[];
  _targetProfile?: Element;
  /**
   * The base specification never makes a rule as to which form is allowed, but implementation guides may do this. See [Aggregation Rules](elementdefinition.html#aggregation) for further clarification.
   */
  versioning?: string;
  _versioning?: Element;
}
/**
 * Code Values for the ElementDefinition.type.aggregation field
 */
export enum ElementDefinitionTypeAggregationCodes {
  CONTAINED = 'contained',
  REFERENCED = 'referenced',
  BUNDLED = 'bundled',
}
/**
 * Code Values for the ElementDefinition.type.versioning field
 */
export enum ElementDefinitionTypeVersioningCodes {
  EITHER = 'either',
  INDEPENDENT = 'independent',
  SPECIFIC = 'specific',
}
/**
 * Examples will most commonly be present for data where it's not implicitly obvious from either the data type or value set what the values might be.  (I.e. Example values for dates or quantities would generally be unnecessary.)  If the example value is fully populated, the publication tool can generate an instance automatically.
 */
export interface ElementDefinitionExample extends Element {
  /**
   * Describes the purpose of this example amoung the set of examples.
   */
  label: string;
  _label?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueBase64Binary: string;
  _valueBase64Binary?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueBoolean: boolean;
  _valueBoolean?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueCanonical: string;
  _valueCanonical?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueCode: string;
  _valueCode?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueDate: string;
  _valueDate?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueDateTime: string;
  _valueDateTime?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueDecimal: number;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueId: string;
  _valueId?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueInstant: string;
  _valueInstant?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueInteger: number;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueMarkdown: string;
  _valueMarkdown?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueOid: string;
  _valueOid?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valuePositiveInt: number;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueString: string;
  _valueString?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueTime: string;
  _valueTime?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueUnsignedInt: number;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueUri: string;
  _valueUri?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueUrl: string;
  _valueUrl?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueUuid: string;
  _valueUuid?: Element;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueAddress: Address;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueAge: Age;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueAnnotation: Annotation;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueAttachment: Attachment;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueCodeableConcept: CodeableConcept;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueCoding: Coding;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueContactPoint: ContactPoint;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueCount: Count;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueDistance: Distance;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueDuration: Duration;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueHumanName: HumanName;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueIdentifier: Identifier;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueMoney: Money;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valuePeriod: Period;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueQuantity: Quantity;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueRange: Range;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueRatio: Ratio;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueReference: Reference;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueSampledData: SampledData;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueSignature: Signature;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueTiming: Timing;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueContactDetail: ContactDetail;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueContributor: Contributor;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueDataRequirement: DataRequirement;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueExpression: Expression;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueParameterDefinition: ParameterDefinition;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueRelatedArtifact: RelatedArtifact;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueTriggerDefinition: TriggerDefinition;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueUsageContext: UsageContext;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueDosage: Dosage;
  /**
   * The actual value for the element, which must be one of the types allowed for this element.
   */
  valueMeta: Meta;
}
/**
 * Constraints should be declared on the "context" element - the lowest element in the hierarchy that is common to all nodes referenced by the constraint.
 */
export interface ElementDefinitionConstraint extends Element {
  /**
   * In the absense of an expression, the expression is likely not enforceable by validators, and might be missed by many systems.
   */
  expression?: string;
  _expression?: Element;
  /**
   * Should be expressed in business terms as much as possible.
   */
  human: string;
  _human?: Element;
  /**
   * Allows identification of which elements have their cardinalities impacted by the constraint.  Will not be referenced for constraints that do not affect cardinality.
   */
  key: string;
  _key?: Element;
  /**
   * To be used if the reason for the constraint might not be intuitive to all implementers.
   */
  requirements?: string;
  _requirements?: Element;
  /**
   * This allows constraints to be asserted as "shall" (error) and "should" (warning).
   */
  severity: string;
  _severity?: Element;
  /**
   * This is used when, e.g. rendering, where it is not useful to present inherited constraints when rendering the snapshot.
   */
  source?: string;
  _source?: Element;
  /**
   * Elements SHALL use "f" as the namespace prefix for the FHIR namespace, and "x" for the xhtml namespace, and SHALL NOT use any other prefixes.     Note: XPath is generally considered not useful because it does not apply to JSON and other formats and because of XSLT implementation issues, and may be removed in the future.
   */
  xpath?: string;
  _xpath?: Element;
}
/**
 * Code Values for the ElementDefinition.constraint.severity field
 */
export enum ElementDefinitionConstraintSeverityCodes {
  ERROR = 'error',
  WARNING = 'warning',
}
/**
 * For a CodeableConcept, when no codes are allowed - only text, use a binding of strength "required" with a description explaining that no coded values are allowed and what sort of information to put in the "text" element.
 */
export interface ElementDefinitionBinding extends Element {
  /**
   * Describes the intended use of this particular set of codes.
   */
  description?: string;
  _description?: Element;
  /**
   * For further discussion, see [Using Terminologies](terminologies.html).
   */
  strength: string;
  _strength?: Element;
  /**
   * The reference may be version-specific or not (e.g. have a |[version] at the end of the canonical URL).
   */
  valueSet?: string;
  _valueSet?: Element;
}
/**
 * Code Values for the ElementDefinition.binding.strength field
 */
export enum ElementDefinitionBindingStrengthCodes {
  REQUIRED = 'required',
  EXTENSIBLE = 'extensible',
  PREFERRED = 'preferred',
  EXAMPLE = 'example',
}
/**
 * Mappings are not necessarily specific enough for safe translation.
 */
export interface ElementDefinitionMapping extends Element {
  /**
   * Comments that provide information about the mapping or its use.
   */
  comment?: string;
  _comment?: Element;
  /**
   * An internal reference to the definition of a mapping.
   */
  identity: string;
  _identity?: Element;
  /**
   * If omitted, then there can be no expectation of computational interpretation of the mapping.
   */
  language?: string;
  _language?: Element;
  /**
   * For most mappings, the syntax is undefined.  Syntax will be provided for mappings to the RIM.  Multiple mappings may be possible and may include constraints on other resource elements that identify when a particular mapping applies.
   */
  map: string;
  _map?: Element;
}
/**
 * Captures constraints on each element within the resource, profile, or extension.
 */
export interface ElementDefinition extends BackboneElement {
  /**
   * Identifies additional names by which this element might also be known.
   */
  alias?: string[];
  _alias?: Element;
  /**
   * The base information does not carry any information that could not be determined from the path and related profiles, but making this determination requires both that the related profiles are available, and that the algorithm to determine them be available. For tooling simplicity, the base information must always be populated in element definitions in snap shots, even if it is the same.
   */
  base?: ElementDefinitionBase;
  /**
   * For a CodeableConcept, when no codes are allowed - only text, use a binding of strength "required" with a description explaining that no coded values are allowed and what sort of information to put in the "text" element.
   */
  binding?: ElementDefinitionBinding;
  /**
   * The concept SHALL be properly aligned with the data element definition and other constraints, as defined in the code system, including relationships, of any code listed here.  Where multiple codes exist in a terminology that could correspond to the data element, the most granular code(s) should be selected, so long as they are not more restrictive than the data element itself. The mappings may be used to provide more or less granular or structured equivalences in the code system.
   */
  code?: Coding[];
  /**
   * If it is possible to capture usage rules using constraints, that mechanism should be used in preference to this element.
   */
  comment?: string;
  _comment?: Element;
  /**
   * A reference to an invariant that may make additional statements about the cardinality or value in the instance.
   */
  condition?: string[];
  _condition?: Element;
  /**
   * Constraints should be declared on the "context" element - the lowest element in the hierarchy that is common to all nodes referenced by the constraint.
   */
  constraint?: ElementDefinitionConstraint[];
  /**
   * ContentReferences can only be defined in specializations, not constrained types, and they cannot be changed and always reference the non-constrained definition.
   */
  contentReference?: string;
  _contentReference?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueBase64Binary?: string;
  _defaultValueBase64Binary?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueBoolean?: boolean;
  _defaultValueBoolean?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueCanonical?: string;
  _defaultValueCanonical?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueCode?: string;
  _defaultValueCode?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueDate?: string;
  _defaultValueDate?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueDateTime?: string;
  _defaultValueDateTime?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueDecimal?: number;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueId?: string;
  _defaultValueId?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueInstant?: string;
  _defaultValueInstant?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueInteger?: number;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueMarkdown?: string;
  _defaultValueMarkdown?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueOid?: string;
  _defaultValueOid?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValuePositiveInt?: number;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueString?: string;
  _defaultValueString?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueTime?: string;
  _defaultValueTime?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueUnsignedInt?: number;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueUri?: string;
  _defaultValueUri?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueUrl?: string;
  _defaultValueUrl?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueUuid?: string;
  _defaultValueUuid?: Element;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueAddress?: Address;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueAge?: Age;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueAnnotation?: Annotation;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueAttachment?: Attachment;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueCodeableConcept?: CodeableConcept;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueCoding?: Coding;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueContactPoint?: ContactPoint;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueCount?: Count;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueDistance?: Distance;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueDuration?: Duration;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueHumanName?: HumanName;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueIdentifier?: Identifier;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueMoney?: Money;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValuePeriod?: Period;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueQuantity?: Quantity;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueRange?: Range;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueRatio?: Ratio;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueReference?: Reference;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueSampledData?: SampledData;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueSignature?: Signature;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueTiming?: Timing;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueContactDetail?: ContactDetail;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueContributor?: Contributor;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueDataRequirement?: DataRequirement;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueExpression?: Expression;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueParameterDefinition?: ParameterDefinition;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueRelatedArtifact?: RelatedArtifact;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueTriggerDefinition?: TriggerDefinition;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueUsageContext?: UsageContext;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueDosage?: Dosage;
  /**
   * Specifying a default value means that the property can never been unknown - it must always have a value. Further, the default value can never be changed, or changed in constraints on content models. Defining default values creates many difficulties in implementation (e.g. when is a value missing?). For these reasons, default values are (and should be) used extremely sparingly.
   * No default values are ever defined in the FHIR specification, nor can they be defined in constraints ("profiles") on data types or resources. This element only exists so that default values may be defined in logical models.
   */
  defaultValueMeta?: Meta;
  /**
   * It is easy for a different definition to change the meaning of an element and this can have nasty downstream consequences. Please be careful when providing definitions in a profile.
   */
  definition?: string;
  _definition?: Element;
  /**
   * Examples will most commonly be present for data where it's not implicitly obvious from either the data type or value set what the values might be.  (I.e. Example values for dates or quantities would generally be unnecessary.)  If the example value is fully populated, the publication tool can generate an instance automatically.
   */
  example?: ElementDefinitionExample[];
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedBase64Binary?: string;
  _fixedBase64Binary?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedBoolean?: boolean;
  _fixedBoolean?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedCanonical?: string;
  _fixedCanonical?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedCode?: string;
  _fixedCode?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedDate?: string;
  _fixedDate?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedDateTime?: string;
  _fixedDateTime?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedDecimal?: number;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedId?: string;
  _fixedId?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedInstant?: string;
  _fixedInstant?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedInteger?: number;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedMarkdown?: string;
  _fixedMarkdown?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedOid?: string;
  _fixedOid?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedPositiveInt?: number;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedString?: string;
  _fixedString?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedTime?: string;
  _fixedTime?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedUnsignedInt?: number;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedUri?: string;
  _fixedUri?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedUrl?: string;
  _fixedUrl?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedUuid?: string;
  _fixedUuid?: Element;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedAddress?: Address;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedAge?: Age;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedAnnotation?: Annotation;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedAttachment?: Attachment;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedCodeableConcept?: CodeableConcept;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedCoding?: Coding;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedContactPoint?: ContactPoint;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedCount?: Count;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedDistance?: Distance;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedDuration?: Duration;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedHumanName?: HumanName;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedIdentifier?: Identifier;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedMoney?: Money;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedPeriod?: Period;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedQuantity?: Quantity;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedRange?: Range;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedRatio?: Ratio;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedReference?: Reference;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedSampledData?: SampledData;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedSignature?: Signature;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedTiming?: Timing;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedContactDetail?: ContactDetail;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedContributor?: Contributor;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedDataRequirement?: DataRequirement;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedExpression?: Expression;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedParameterDefinition?: ParameterDefinition;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedRelatedArtifact?: RelatedArtifact;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedTriggerDefinition?: TriggerDefinition;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedUsageContext?: UsageContext;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedDosage?: Dosage;
  /**
   * This is not recommended for Coding and CodeableConcept since these often have highly contextual properties such as version or display.
   */
  fixedMeta?: Meta;
  /**
   * Only the definition of an element can set IsModifier true - either the specification itself or where an extension is originally defined. Once set, it cannot be changed in derived profiles. An element/extension that has isModifier=true SHOULD also have a minimum cardinality of 1, so that there is no lack of clarity about what to do if it is missing. If it can be missing, the definition SHALL make the meaning of a missing element clear.
   */
  isModifier?: boolean;
  _isModifier?: Element;
  /**
   * Explains how that element affects the interpretation of the resource or element that contains it.
   */
  isModifierReason?: string;
  _isModifierReason?: Element;
  /**
   * Some resources include a set of simple metadata, and some very large data. This element is used to reduce the quantity of data returned in searches. Note that servers may pre-cache summarized resources for optimal performance, so servers might not support per-profile use of the isSummary flag. When a request is made with _summary=true, serailisers only include elements marked as 'isSummary = true'. Other than Attachment.data, all data type properties are included in the summary form. In resource and data type definitions, if an element is at the root or has a parent that is 'mustSupport' and the minimum cardinality is 1 or the element is a modifier, it must be marked as isSummary=true.
   */
  isSummary?: boolean;
  _isSummary?: Element;
  /**
   * See also the extension (http://hl7.org/fhir/StructureDefinition/elementdefinition-question)[extension-elementdefinition-question.html].
   */
  label?: string;
  _label?: Element;
  /**
   * Mappings are not necessarily specific enough for safe translation.
   */
  mapping?: ElementDefinitionMapping[];
  /**
   * The maximum number of times this element is permitted to appear in the instance.
   */
  max?: string;
  _max?: Element;
  /**
   * Receivers are not required to reject instances that exceed the maximum length.  The full length could be stored.  In some cases, data might be truncated, though truncation should be undertaken with care and an understanding of the consequences of doing so. If not specified, there is no conformance expectation for length support.
   */
  maxLength?: number;
  /**
   * Except for date/date/instant, the type of the maxValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of maxValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is added to the current clock to determine the maximum allowable value.   A maximum value for a Quantity is interpreted as an canonical maximum - e.g. you cannot provide 10g if the maximum value is 50mg.
   */
  maxValueDate?: string;
  _maxValueDate?: Element;
  /**
   * Except for date/date/instant, the type of the maxValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of maxValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is added to the current clock to determine the maximum allowable value.   A maximum value for a Quantity is interpreted as an canonical maximum - e.g. you cannot provide 10g if the maximum value is 50mg.
   */
  maxValueDateTime?: string;
  _maxValueDateTime?: Element;
  /**
   * Except for date/date/instant, the type of the maxValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of maxValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is added to the current clock to determine the maximum allowable value.   A maximum value for a Quantity is interpreted as an canonical maximum - e.g. you cannot provide 10g if the maximum value is 50mg.
   */
  maxValueInstant?: string;
  _maxValueInstant?: Element;
  /**
   * Except for date/date/instant, the type of the maxValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of maxValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is added to the current clock to determine the maximum allowable value.   A maximum value for a Quantity is interpreted as an canonical maximum - e.g. you cannot provide 10g if the maximum value is 50mg.
   */
  maxValueTime?: string;
  _maxValueTime?: Element;
  /**
   * Except for date/date/instant, the type of the maxValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of maxValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is added to the current clock to determine the maximum allowable value.   A maximum value for a Quantity is interpreted as an canonical maximum - e.g. you cannot provide 10g if the maximum value is 50mg.
   */
  maxValueDecimal?: number;
  /**
   * Except for date/date/instant, the type of the maxValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of maxValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is added to the current clock to determine the maximum allowable value.   A maximum value for a Quantity is interpreted as an canonical maximum - e.g. you cannot provide 10g if the maximum value is 50mg.
   */
  maxValueInteger?: number;
  /**
   * Except for date/date/instant, the type of the maxValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of maxValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is added to the current clock to determine the maximum allowable value.   A maximum value for a Quantity is interpreted as an canonical maximum - e.g. you cannot provide 10g if the maximum value is 50mg.
   */
  maxValuePositiveInt?: number;
  /**
   * Except for date/date/instant, the type of the maxValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of maxValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is added to the current clock to determine the maximum allowable value.   A maximum value for a Quantity is interpreted as an canonical maximum - e.g. you cannot provide 10g if the maximum value is 50mg.
   */
  maxValueUnsignedInt?: number;
  /**
   * Except for date/date/instant, the type of the maxValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of maxValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is added to the current clock to determine the maximum allowable value.   A maximum value for a Quantity is interpreted as an canonical maximum - e.g. you cannot provide 10g if the maximum value is 50mg.
   */
  maxValueQuantity?: Quantity;
  /**
   * Implicit meanings for missing values can only be specified on a resource, data type, or extension definition, and never in a profile that applies to one of these. An implicit meaning for a missing value can never be changed, and specifying one has the consequence that constraining its use in profiles eliminates use cases as possibilities, not merely moving them out of scope.
   */
  meaningWhenMissing?: string;
  _meaningWhenMissing?: Element;
  /**
   * The minimum number of times this element SHALL appear in the instance.
   */
  min?: number;
  /**
   * Except for date/date/instant, the type of the minValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of minValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is subtracted from the current clock to determine the minimum allowable value.   A minimum value for a Quantity is interpreted as an canonical minimum - e.g. you cannot provide 100mg if the minimum value is 10g.
   */
  minValueDate?: string;
  _minValueDate?: Element;
  /**
   * Except for date/date/instant, the type of the minValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of minValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is subtracted from the current clock to determine the minimum allowable value.   A minimum value for a Quantity is interpreted as an canonical minimum - e.g. you cannot provide 100mg if the minimum value is 10g.
   */
  minValueDateTime?: string;
  _minValueDateTime?: Element;
  /**
   * Except for date/date/instant, the type of the minValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of minValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is subtracted from the current clock to determine the minimum allowable value.   A minimum value for a Quantity is interpreted as an canonical minimum - e.g. you cannot provide 100mg if the minimum value is 10g.
   */
  minValueInstant?: string;
  _minValueInstant?: Element;
  /**
   * Except for date/date/instant, the type of the minValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of minValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is subtracted from the current clock to determine the minimum allowable value.   A minimum value for a Quantity is interpreted as an canonical minimum - e.g. you cannot provide 100mg if the minimum value is 10g.
   */
  minValueTime?: string;
  _minValueTime?: Element;
  /**
   * Except for date/date/instant, the type of the minValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of minValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is subtracted from the current clock to determine the minimum allowable value.   A minimum value for a Quantity is interpreted as an canonical minimum - e.g. you cannot provide 100mg if the minimum value is 10g.
   */
  minValueDecimal?: number;
  /**
   * Except for date/date/instant, the type of the minValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of minValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is subtracted from the current clock to determine the minimum allowable value.   A minimum value for a Quantity is interpreted as an canonical minimum - e.g. you cannot provide 100mg if the minimum value is 10g.
   */
  minValueInteger?: number;
  /**
   * Except for date/date/instant, the type of the minValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of minValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is subtracted from the current clock to determine the minimum allowable value.   A minimum value for a Quantity is interpreted as an canonical minimum - e.g. you cannot provide 100mg if the minimum value is 10g.
   */
  minValuePositiveInt?: number;
  /**
   * Except for date/date/instant, the type of the minValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of minValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is subtracted from the current clock to determine the minimum allowable value.   A minimum value for a Quantity is interpreted as an canonical minimum - e.g. you cannot provide 100mg if the minimum value is 10g.
   */
  minValueUnsignedInt?: number;
  /**
   * Except for date/date/instant, the type of the minValue[x] SHALL be the same as the specified type of the element. For the date/dateTime/instant values, the type of minValue[x] SHALL be either the same, or a [Duration](datatypes.html#Duration) which specifies a relative time limit to the current time. The duration value is positive, and is subtracted from the current clock to determine the minimum allowable value.   A minimum value for a Quantity is interpreted as an canonical minimum - e.g. you cannot provide 100mg if the minimum value is 10g.
   */
  minValueQuantity?: Quantity;
  /**
   * "Something useful" is context dependent and impossible to describe in the base FHIR specification. For this reason, tue mustSupport flag is never set to true by the FHIR specification itself - it is only set to true in profiles.  A profile on a type can always make musSupport = true if it is false in the base type but cannot make mustSupport = false if it is true in the base type.   This is done in [Resource Profiles](profiling.html#mustsupport), where the profile labels an element as mustSupport=true. When a profile does this, it SHALL also make clear exactly what kind of "support" is required, as this can mean many things.    Note that an element that has the property IsModifier is not necessarily a "key" element (e.g. one of the important elements to make use of the resource), nor is it automatically mustSupport - however both of these things are more likely to be true for IsModifier elements than for other elements.
   */
  mustSupport?: boolean;
  _mustSupport?: Element;
  /**
   * This element can only be asserted on repeating elements and can only be introduced when defining resources or data types.  It can be further refined profiled elements but if absent in the base type, a profile cannot assert meaning.
   */
  orderMeaning?: string;
  _orderMeaning?: Element;
  /**
   * The path identifies the element and is expressed as a "."-separated list of ancestor elements, beginning with the name of the resource or extension.
   */
  path: string;
  _path?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternBase64Binary?: string;
  _patternBase64Binary?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternBoolean?: boolean;
  _patternBoolean?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternCanonical?: string;
  _patternCanonical?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternCode?: string;
  _patternCode?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternDate?: string;
  _patternDate?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternDateTime?: string;
  _patternDateTime?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternDecimal?: number;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternId?: string;
  _patternId?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternInstant?: string;
  _patternInstant?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternInteger?: number;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternMarkdown?: string;
  _patternMarkdown?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternOid?: string;
  _patternOid?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternPositiveInt?: number;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternString?: string;
  _patternString?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternTime?: string;
  _patternTime?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternUnsignedInt?: number;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternUri?: string;
  _patternUri?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternUrl?: string;
  _patternUrl?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternUuid?: string;
  _patternUuid?: Element;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternAddress?: Address;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternAge?: Age;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternAnnotation?: Annotation;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternAttachment?: Attachment;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternCodeableConcept?: CodeableConcept;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternCoding?: Coding;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternContactPoint?: ContactPoint;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternCount?: Count;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternDistance?: Distance;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternDuration?: Duration;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternHumanName?: HumanName;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternIdentifier?: Identifier;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternMoney?: Money;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternPeriod?: Period;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternQuantity?: Quantity;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternRange?: Range;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternRatio?: Ratio;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternReference?: Reference;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternSampledData?: SampledData;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternSignature?: Signature;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternTiming?: Timing;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternContactDetail?: ContactDetail;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternContributor?: Contributor;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternDataRequirement?: DataRequirement;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternExpression?: Expression;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternParameterDefinition?: ParameterDefinition;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternRelatedArtifact?: RelatedArtifact;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternTriggerDefinition?: TriggerDefinition;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternUsageContext?: UsageContext;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternDosage?: Dosage;
  /**
   * Mostly used for fixing values of CodeableConcept. In general, pattern[x] is not intended for use with primitive types, where is has the same meaning as fixed[x].
   */
  patternMeta?: Meta;
  /**
   * In resources, this is rarely used except for special cases where the representation deviates from the normal, and can only be done in the base standard (and profiles must reproduce what the base standard does). This element is used quite commonly in Logical models when the logical models represent a specific serialization format (e.g. CDA, v2 etc.).
   */
  representation?: string[];
  _representation?: Element;
  /**
   * This element does not describe the usage of the element (that's done in comments), rather it's for traceability of *why* the element is either needed or why the constraints exist as they do.  This may be used to point to source materials or specifications that drove the structure of this data element.
   */
  requirements?: string;
  _requirements?: Element;
  /**
   * It is easy for a different short definition to change the meaning of an element and this can have nasty downstream consequences. Please be careful when providing short definitions in a profile.
   */
  short?: string;
  _short?: Element;
  /**
   * If set to true, an ancestor profile SHALL have a slicing definition with this name.  If set to false, no ancestor profile is permitted to have a slicing definition with this name.
   */
  sliceIsConstraining?: boolean;
  _sliceIsConstraining?: Element;
  /**
   * The name SHALL be unique within the structure within the context of the constrained resource element.  (Though to avoid confusion, uniqueness across all elements is recommended.).
   */
  sliceName?: string;
  _sliceName?: Element;
  /**
   * The first element in the sequence, the one that carries the slicing, is the definition that applies to all the slices. This is based on the unconstrained element, but can apply any constraints as appropriate. This may include the common constraints on the children of the element.
   */
  slicing?: ElementDefinitionSlicing;
  /**
   * The Type of the element can be left blank in a differential constraint, in which case the type is inherited from the resource. Abstract types are not permitted to appear as a type when multiple types are listed.  (I.e. Abstract types cannot be part of a choice).
   */
  type?: ElementDefinitionType[];
}
/**
 * Code Values for the ElementDefinition.representation field
 */
export enum ElementDefinitionRepresentationCodes {
  XMLATTR = 'xmlAttr',
  XMLTEXT = 'xmlText',
  TYPEATTR = 'typeAttr',
  CDATEXT = 'cdaText',
  XHTML = 'xhtml',
}
/**
 * A expression that is evaluated in a specified context and returns a value. The context of use of the expression must specify the context in which the expression is evaluated, and how the result of the expression is used.
 */
export interface Expression extends Element {
  /**
   * A brief, natural language description of the condition that effectively communicates the intended semantics.
   */
  description?: string;
  _description?: Element;
  /**
   * An expression in the specified language that returns a value.
   */
  expression?: string;
  _expression?: Element;
  /**
   * The media type of the language for the expression.
   */
  language: string;
  _language?: Element;
  /**
   * A short name assigned to the expression to allow for multiple reuse of the expression in the context where it is defined.
   */
  name?: string;
  _name?: Element;
  /**
   * If both a reference and an expression is found, the reference SHALL point to the same expression.
   */
  reference?: string;
  _reference?: Element;
}
/**
 * Code Values for the Expression.language field
 */
export enum ExpressionLanguageCodes {
  TEXT_CQL = 'text/cql',
  TEXT_FHIRPATH = 'text/fhirpath',
  APPLICATION_X_FHIR_QUERY = 'application/x-fhir-query',
  ETC = 'etc.',
}
/**
 * Optional Extension Element - found in all resources.
 */
export interface Extension extends Element {
  /**
   * The definition may point directly to a computable or human-readable definition of the extensibility codes, or it may be a logical URI as declared in some other specification. The definition SHALL be a URI for the Structure Definition defining the extension.
   */
  url: string;
  _url?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueBase64Binary?: string;
  _valueBase64Binary?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueBoolean?: boolean;
  _valueBoolean?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueCanonical?: string;
  _valueCanonical?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueCode?: string;
  _valueCode?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueDate?: string;
  _valueDate?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueDateTime?: string;
  _valueDateTime?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueDecimal?: number;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueId?: string;
  _valueId?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueInstant?: string;
  _valueInstant?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueInteger?: number;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueMarkdown?: string;
  _valueMarkdown?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueOid?: string;
  _valueOid?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valuePositiveInt?: number;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueString?: string;
  _valueString?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueTime?: string;
  _valueTime?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueUnsignedInt?: number;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueUri?: string;
  _valueUri?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueUrl?: string;
  _valueUrl?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueUuid?: string;
  _valueUuid?: Element;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueAddress?: Address;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueAge?: Age;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueAnnotation?: Annotation;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueAttachment?: Attachment;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueCodeableConcept?: CodeableConcept;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueCoding?: Coding;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueContactPoint?: ContactPoint;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueCount?: Count;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueDistance?: Distance;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueDuration?: Duration;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueHumanName?: HumanName;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueIdentifier?: Identifier;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueMoney?: Money;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valuePeriod?: Period;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueQuantity?: Quantity;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueRange?: Range;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueRatio?: Ratio;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueReference?: Reference;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueSampledData?: SampledData;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueSignature?: Signature;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueTiming?: Timing;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueContactDetail?: ContactDetail;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueContributor?: Contributor;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueDataRequirement?: DataRequirement;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueExpression?: Expression;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueParameterDefinition?: ParameterDefinition;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueRelatedArtifact?: RelatedArtifact;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueTriggerDefinition?: TriggerDefinition;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueUsageContext?: UsageContext;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueDosage?: Dosage;
  /**
   * Value of extension - must be one of a constrained set of the data types (see [Extensibility](extensibility.html) for a list).
   */
  valueMeta?: Meta;
}
/**
 * A human's name with the ability to identify parts and usage.
 */
export interface HumanName extends Element {
  /**
   * Family Name may be decomposed into specific parts using extensions (de, nl, es related cultures).
   */
  family?: string;
  _family?: Element;
  /**
   * If only initials are recorded, they may be used in place of the full name parts. Initials may be separated into multiple given names but often aren't due to paractical limitations.  This element is not called "first name" since given names do not always come first.
   */
  given?: string[];
  _given?: Element;
  /**
   * Indicates the period of time when this name was valid for the named person.
   */
  period?: Period;
  /**
   * Part of the name that is acquired as a title due to academic, legal, employment or nobility status, etc. and that appears at the start of the name.
   */
  prefix?: string[];
  _prefix?: Element;
  /**
   * Part of the name that is acquired as a title due to academic, legal, employment or nobility status, etc. and that appears at the end of the name.
   */
  suffix?: string[];
  _suffix?: Element;
  /**
   * Can provide both a text representation and parts. Applications updating a name SHALL ensure that when both text and parts are present,  no content is included in the text that isn't found in a part.
   */
  text?: string;
  _text?: Element;
  /**
   * Applications can assume that a name is current unless it explicitly says that it is temporary or old.
   */
  use?: string;
  _use?: Element;
}
/**
 * Code Values for the HumanName.use field
 */
export enum HumanNameUseCodes {
  USUAL = 'usual',
  OFFICIAL = 'official',
  TEMP = 'temp',
  NICKNAME = 'nickname',
  ANONYMOUS = 'anonymous',
  OLD = 'old',
  MAIDEN = 'maiden',
}
/**
 * An identifier - identifies some entity uniquely and unambiguously. Typically this is used for business identifiers.
 */
export interface Identifier extends Element {
  /**
   * The Identifier.assigner may omit the .reference element and only contain a .display element reflecting the name or other textual information about the assigning organization.
   */
  assigner?: Reference;
  /**
   * Time period during which identifier is/was valid for use.
   */
  period?: Period;
  /**
   * Identifier.system is always case sensitive.
   */
  system?: string;
  _system?: Element;
  /**
   * This element deals only with general categories of identifiers.  It SHOULD not be used for codes that correspond 1..1 with the Identifier.system. Some identifiers may fall into multiple categories due to common usage.   Where the system is known, a type is unnecessary because the type is always part of the system definition. However systems often need to handle identifiers where the system is not known. There is not a 1:1 relationship between type and system, since many different systems have the same type.
   */
  type?: CodeableConcept;
  /**
   * Applications can assume that an identifier is permanent unless it explicitly says that it is temporary.
   */
  use?: string;
  _use?: Element;
  /**
   * If the value is a full URI, then the system SHALL be urn:ietf:rfc:3986.  The value's primary purpose is computational mapping.  As a result, it may be normalized for comparison purposes (e.g. removing non-significant whitespace, dashes, etc.)  A value formatted for human display can be conveyed using the [Rendered Value extension](extension-rendered-value.html). Identifier.value is to be treated as case sensitive unless knowledge of the Identifier.system allows the processer to be confident that non-case-sensitive processing is safe.
   */
  value?: string;
  _value?: Element;
}
/**
 * Code Values for the Identifier.use field
 */
export enum IdentifierUseCodes {
  USUAL = 'usual',
  OFFICIAL = 'official',
  TEMP = 'temp',
  SECONDARY = 'secondary',
  OLD = 'old',
}
/**
 * The marketing status describes the date when a medicinal product is actually put on the market or the date as of which it is no longer available.
 */
export interface MarketingStatus extends BackboneElement {
  /**
   * The country in which the marketing authorisation has been granted shall be specified It should be specified using the ISO 3166 ‑ 1 alpha-2 code elements.
   */
  country: CodeableConcept;
  /**
   * The date when the Medicinal Product is placed on the market by the Marketing Authorisation Holder (or where applicable, the manufacturer/distributor) in a country and/or jurisdiction shall be provided A complete date consisting of day, month and year shall be specified using the ISO 8601 date format NOTE “Placed on the market” refers to the release of the Medicinal Product into the distribution chain.
   */
  dateRange: Period;
  /**
   * Where a Medicines Regulatory Agency has granted a marketing authorisation for which specific provisions within a jurisdiction apply, the jurisdiction can be specified using an appropriate controlled terminology The controlled term and the controlled term identifier shall be specified.
   */
  jurisdiction?: CodeableConcept;
  /**
   * The date when the Medicinal Product is placed on the market by the Marketing Authorisation Holder (or where applicable, the manufacturer/distributor) in a country and/or jurisdiction shall be provided A complete date consisting of day, month and year shall be specified using the ISO 8601 date format NOTE “Placed on the market” refers to the release of the Medicinal Product into the distribution chain.
   */
  restoreDate?: string;
  _restoreDate?: Element;
  /**
   * This attribute provides information on the status of the marketing of the medicinal product See ISO/TS 20443 for more information and examples.
   */
  status: CodeableConcept;
}
/**
 * The metadata about a resource. This is content in the resource that is maintained by the infrastructure. Changes to the content might not always be associated with version changes to the resource.
 */
export interface Meta extends Element {
  /**
   * This value is always populated except when the resource is first being created. The server / resource manager sets this value; what a client provides is irrelevant. This is equivalent to the HTTP Last-Modified and SHOULD have the same value on a [read](http.html#read) interaction.
   */
  lastUpdated?: string;
  _lastUpdated?: Element;
  /**
   * It is up to the server and/or other infrastructure of policy to determine whether/how these claims are verified and/or updated over time.  The list of profile URLs is a set.
   */
  profile?: string[];
  _profile?: Element;
  /**
   * The security labels can be updated without changing the stated version of the resource. The list of security labels is a set. Uniqueness is based the system/code, and version and display are ignored.
   */
  security?: Coding[];
  /**
   * In the provenance resource, this corresponds to Provenance.entity.what[x]. The exact use of the source (and the implied Provenance.entity.role) is left to implementer discretion. Only one nominated source is allowed; for additional provenance details, a full Provenance resource should be used.
   * This element can be used to indicate where the current master source of a resource that has a canonical URL if the resource is no longer hosted at the canonical URL.
   */
  source?: string;
  _source?: Element;
  /**
   * The tags can be updated without changing the stated version of the resource. The list of tags is a set. Uniqueness is based the system/code, and version and display are ignored.
   */
  tag?: Coding[];
  /**
   * The server assigns this value, and ignores what the client specifies, except in the case that the server is imposing version integrity on updates/deletes.
   */
  versionId?: string;
  _versionId?: Element;
}
/**
 * An amount of economic utility in some recognized currency.
 */
export interface Money extends Element {
  /**
   * ISO 4217 Currency Code.
   */
  currency?: string;
  _currency?: Element;
  /**
   * Monetary values have their own rules for handling precision (refer to standard accounting text books).
   */
  value?: number;
}
/**
 * There SHALL be a code if there is a value and it SHALL be an expression of currency.  If system is present, it SHALL be ISO 4217 (system = "urn:iso:std:iso:4217" - currency).
 */
export interface MoneyQuantity extends Quantity {
  /**
   * The preferred system is UCUM, but SNOMED CT can also be used (for customary units) or ISO 4217 for currency.  The context of use may additionally require a code from a particular system.
   */
  code?: string;
  _code?: Element;
  /**
   * How the value should be understood and represented - whether the actual value is greater or less than the stated value due to measurement issues; e.g. if the comparator is "<" , then the real value is < stated value.
   */
  comparator?: string;
  _comparator?: Element;
  /**
   * The identification of the system that provides the coded form of the unit.
   */
  system?: string;
  _system?: Element;
  /**
   * A human-readable form of the unit.
   */
  unit?: string;
  _unit?: Element;
  /**
   * The implicit precision in the value should always be honored. Monetary values have their own rules for handling precision (refer to standard accounting text books).
   */
  value?: number;
}
/**
 * Code Values for the Quantity.comparator field
 */
export enum QuantityComparatorCodes {
  LESS_THAN = '<',
  LESS_THAN_OR_EQUALS = '<=',
  GREATER_THAN_OR_EQUALS = '>=',
  GREATER_THAN = '>',
}
/**
 * A human-readable summary of the resource conveying the essential clinical and business information for the resource.
 */
export interface Narrative extends Element {
  /**
   * The contents of the html element are an XHTML fragment containing only the basic html formatting elements described in chapters 7-11 and 15 of the HTML 4.0 standard, <a> elements (either name or href), images and internally contained stylesheets. The XHTML content SHALL NOT contain a head, a body, external stylesheet references, scripts, forms, base/link/xlink, frames, iframes and objects.
   */
  div: string;
  _div?: Element;
  /**
   * The status of the narrative - whether it's entirely generated (from just the defined data or the extensions too), or whether a human authored it and it may contain additional data.
   */
  status: string;
  _status?: Element;
}
/**
 * Code Values for the Narrative.status field
 */
export enum NarrativeStatusCodes {
  GENERATED = 'generated',
  EXTENSIONS = 'extensions',
  ADDITIONAL = 'additional',
  EMPTY = 'empty',
}
/**
 * The parameters to the module. This collection specifies both the input and output parameters. Input parameters are provided by the caller as part of the $evaluate operation. Output parameters are included in the GuidanceResponse.
 */
export interface ParameterDefinition extends Element {
  /**
   * A brief discussion of what the parameter is for and how it is used by the module.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * The maximum number of times this element is permitted to appear in the request or response.
   */
  max?: string;
  _max?: Element;
  /**
   * The minimum number of times this parameter SHALL appear in the request or response.
   */
  min?: number;
  /**
   * The name of the parameter used to allow access to the value of the parameter in evaluation contexts.
   */
  name?: string;
  _name?: Element;
  /**
   * If specified, this indicates a profile that the input data must conform to, or that the output data will conform to.
   */
  profile?: string;
  _profile?: Element;
  /**
   * The type of the parameter.
   */
  type: string;
  _type?: Element;
  /**
   * Whether the parameter is input or output for the module.
   */
  use: string;
  _use?: Element;
}
/**
 * Code Values for the ParameterDefinition.use field
 */
export enum ParameterDefinitionUseCodes {
  IN = 'in',
  OUT = 'out',
}
/**
 * A time period defined by a start and end date and optionally time.
 */
export interface Period extends Element {
  /**
   * The high value includes any matching date/time. i.e. 2012-02-03T10:00:00 is in a period that has an end value of 2012-02-03.
   */
  end?: string;
  _end?: Element;
  /**
   * If the low element is missing, the meaning is that the low boundary is not known.
   */
  start?: string;
  _start?: Element;
}
/**
 * A populatioof people with some set of grouping criteria.
 */
export interface Population extends BackboneElement {
  /**
   * The age of the specific population.
   */
  ageRange?: Range;
  /**
   * The age of the specific population.
   */
  ageCodeableConcept?: CodeableConcept;
  /**
   * The gender of the specific population.
   */
  gender?: CodeableConcept;
  /**
   * The existing physiological conditions of the specific population to which this applies.
   */
  physiologicalCondition?: CodeableConcept;
  /**
   * Race of the specific population.
   */
  race?: CodeableConcept;
}
/**
 * The marketing status describes the date when a medicinal product is actually put on the market or the date as of which it is no longer available.
 */
export interface ProdCharacteristic extends BackboneElement {
  /**
   * Where applicable, the color can be specified An appropriate controlled vocabulary shall be used The term and the term identifier shall be used.
   */
  color?: string[];
  _color?: Element;
  /**
   * Where applicable, the depth can be specified using a numerical value and its unit of measurement The unit of measurement shall be specified in accordance with ISO 11240 and the resulting terminology The symbol and the symbol identifier shall be used.
   */
  depth?: Quantity;
  /**
   * Where applicable, the external diameter can be specified using a numerical value and its unit of measurement The unit of measurement shall be specified in accordance with ISO 11240 and the resulting terminology The symbol and the symbol identifier shall be used.
   */
  externalDiameter?: Quantity;
  /**
   * Where applicable, the height can be specified using a numerical value and its unit of measurement The unit of measurement shall be specified in accordance with ISO 11240 and the resulting terminology The symbol and the symbol identifier shall be used.
   */
  height?: Quantity;
  /**
   * Where applicable, the image can be provided The format of the image attachment shall be specified by regional implementations.
   */
  image?: Attachment[];
  /**
   * Where applicable, the imprint can be specified as text.
   */
  imprint?: string[];
  _imprint?: Element;
  /**
   * Where applicable, the nominal volume can be specified using a numerical value and its unit of measurement The unit of measurement shall be specified in accordance with ISO 11240 and the resulting terminology The symbol and the symbol identifier shall be used.
   */
  nominalVolume?: Quantity;
  /**
   * Where applicable, the scoring can be specified An appropriate controlled vocabulary shall be used The term and the term identifier shall be used.
   */
  scoring?: CodeableConcept;
  /**
   * Where applicable, the shape can be specified An appropriate controlled vocabulary shall be used The term and the term identifier shall be used.
   */
  shape?: string;
  _shape?: Element;
  /**
   * Where applicable, the weight can be specified using a numerical value and its unit of measurement The unit of measurement shall be specified in accordance with ISO 11240 and the resulting terminology The symbol and the symbol identifier shall be used.
   */
  weight?: Quantity;
  /**
   * Where applicable, the width can be specified using a numerical value and its unit of measurement The unit of measurement shall be specified in accordance with ISO 11240 and the resulting terminology The symbol and the symbol identifier shall be used.
   */
  width?: Quantity;
}
/**
 * The shelf-life and storage information for a medicinal product item or container can be described using this class.
 */
export interface ProductShelfLife extends BackboneElement {
  /**
   * Unique identifier for the packaged Medicinal Product.
   */
  identifier?: Identifier;
  /**
   * The shelf life time period can be specified using a numerical value for the period of time and its unit of time measurement The unit of measurement shall be specified in accordance with ISO 11240 and the resulting terminology The symbol and the symbol identifier shall be used.
   */
  period: Quantity;
  /**
   * Special precautions for storage, if any, can be specified using an appropriate controlled vocabulary The controlled term and the controlled term identifier shall be specified.
   */
  specialPrecautionsForStorage?: CodeableConcept[];
  /**
   * This describes the shelf life, taking into account various scenarios such as shelf life of the packaged Medicinal Product itself, shelf life after transformation where necessary and shelf life after the first opening of a bottle, etc. The shelf life type shall be specified using an appropriate controlled vocabulary The controlled term and the controlled term identifier shall be specified.
   */
  type: CodeableConcept;
}
/**
 * A measured amount (or an amount that can potentially be measured). Note that measured amounts include amounts that are not precisely quantified, including amounts involving arbitrary units and floating currencies.
 */
export interface Quantity extends Element {
  /**
   * The preferred system is UCUM, but SNOMED CT can also be used (for customary units) or ISO 4217 for currency.  The context of use may additionally require a code from a particular system.
   */
  code?: string;
  _code?: Element;
  /**
   * How the value should be understood and represented - whether the actual value is greater or less than the stated value due to measurement issues; e.g. if the comparator is "<" , then the real value is < stated value.
   */
  comparator?: string;
  _comparator?: Element;
  /**
   * The identification of the system that provides the coded form of the unit.
   */
  system?: string;
  _system?: Element;
  /**
   * A human-readable form of the unit.
   */
  unit?: string;
  _unit?: Element;
  /**
   * The implicit precision in the value should always be honored. Monetary values have their own rules for handling precision (refer to standard accounting text books).
   */
  value?: number;
}
/**
 * A set of ordered Quantities defined by a low and high limit.
 */
export interface Range extends Element {
  /**
   * If the high element is missing, the high boundary is not known.
   */
  high?: Quantity;
  /**
   * If the low element is missing, the low boundary is not known.
   */
  low?: Quantity;
}
/**
 * A relationship of two Quantity values - expressed as a numerator and a denominator.
 */
export interface Ratio extends Element {
  /**
   * The value of the denominator.
   */
  denominator?: Quantity;
  /**
   * The value of the numerator.
   */
  numerator?: Quantity;
}
/**
 * A reference from one resource to another.
 */
export interface Reference extends Element {
  /**
   * This is generally not the same as the Resource.text of the referenced resource.  The purpose is to identify what's being referenced, not to fully describe it.
   */
  display?: string;
  _display?: Element;
  /**
   * When an identifier is provided in place of a reference, any system processing the reference will only be able to resolve the identifier to a reference if it understands the business context in which the identifier is used. Sometimes this is global (e.g. a national identifier) but often it is not. For this reason, none of the useful mechanisms described for working with references (e.g. chaining, includes) are possible, nor should servers be expected to be able resolve the reference. Servers may accept an identifier based reference untouched, resolve it, and/or reject it - see CapabilityStatement.rest.resource.referencePolicy.
   * When both an identifier and a literal reference are provided, the literal reference is preferred. Applications processing the resource are allowed - but not required - to check that the identifier matches the literal reference
   * Applications converting a logical reference to a literal reference may choose to leave the logical reference present, or remove it.
   * Reference is intended to point to a structure that can potentially be expressed as a FHIR resource, though there is no need for it to exist as an actual FHIR resource instance - except in as much as an application wishes to actual find the target of the reference. The content referred to be the identifier must meet the logical constraints implied by any limitations on what resource types are permitted for the reference.  For example, it would not be legitimate to send the identifier for a drug prescription if the type were Reference(Observation|DiagnosticReport).  One of the use-cases for Reference.identifier is the situation where no FHIR representation exists (where the type is Reference (Any).
   */
  identifier?: Identifier;
  /**
   * Using absolute URLs provides a stable scalable approach suitable for a cloud/web context, while using relative/logical references provides a flexible approach suitable for use when trading across closed eco-system boundaries.   Absolute URLs do not need to point to a FHIR RESTful server, though this is the preferred approach. If the URL conforms to the structure "/[type]/[id]" then it should be assumed that the reference is to a FHIR RESTful server.
   */
  reference?: string;
  _reference?: Element;
  /**
   * This element is used to indicate the type of  the target of the reference. This may be used which ever of the other elements are populated (or not). In some cases, the type of the target may be determined by inspection of the reference (e.g. a RESTful URL) or by resolving the target of the reference; if both the type and a reference is provided, the reference SHALL resolve to a resource of the same type as that specified.
   */
  type?: string;
  _type?: Element;
}
/**
 * Related artifacts such as additional documentation, justification, or bibliographic references.
 */
export interface RelatedArtifact extends Element {
  /**
   * Additional structured information about citations should be captured as extensions.
   */
  citation?: string;
  _citation?: Element;
  /**
   * A brief description of the document or knowledge resource being referenced, suitable for display to a consumer.
   */
  display?: string;
  _display?: Element;
  /**
   * The document being referenced, represented as an attachment. This is exclusive with the resource element.
   */
  document?: Attachment;
  /**
   * A short label that can be used to reference the citation from elsewhere in the containing artifact, such as a footnote index.
   */
  label?: string;
  _label?: Element;
  /**
   * If the type is predecessor, this is a reference to the succeeding knowledge resource. If the type is successor, this is a reference to the prior knowledge resource.
   */
  resource?: string;
  _resource?: Element;
  /**
   * The type of relationship to the related artifact.
   */
  type: string;
  _type?: Element;
  /**
   * If a document or resource element is present, this element SHALL NOT be provided (use the url or reference in the Attachment or resource reference).
   */
  url?: string;
  _url?: Element;
}
/**
 * Code Values for the RelatedArtifact.type field
 */
export enum RelatedArtifactTypeCodes {
  DOCUMENTATION = 'documentation',
  JUSTIFICATION = 'justification',
  CITATION = 'citation',
  PREDECESSOR = 'predecessor',
  SUCCESSOR = 'successor',
  DERIVED_FROM = 'derived-from',
  DEPENDS_ON = 'depends-on',
  COMPOSED_OF = 'composed-of',
}
/**
 * A series of measurements taken by a device, with upper and lower limits. There may be more than one dimension in the data.
 */
export interface SampledData extends Element {
  /**
   * Data may be missing if it is omitted for summarization purposes. In general, data is required for any actual use of a SampledData.
   */
  data?: string;
  _data?: Element;
  /**
   * If there is more than one dimension, the code for the type of data will define the meaning of the dimensions (typically ECG data).
   */
  dimensions: number;
  /**
   * A correction factor that is applied to the sampled data points before they are added to the origin.
   */
  factor?: number;
  /**
   * The lower limit of detection of the measured points. This is needed if any of the data points have the value "L" (lower than detection limit).
   */
  lowerLimit?: number;
  /**
   * The base quantity that a measured value of zero represents. In addition, this provides the units of the entire measurement series.
   */
  origin: Quantity;
  /**
   * This is usually a whole number.
   */
  period: number;
  /**
   * The upper limit of detection of the measured points. This is needed if any of the data points have the value "U" (higher than detection limit).
   */
  upperLimit?: number;
}
/**
 * A signature along with supporting context. The signature may be a digital signature that is cryptographic in nature, or some other signature acceptable to the domain. This other signature may be as simple as a graphical image representing a hand-written signature, or a signature ceremony Different signature approaches have different utilities.
 */
export interface Signature extends Element {
  /**
   * Where the signature type is an XML DigSig, the signed content is a FHIR Resource(s), the signature is of the XML form of the Resource(s) using  XML-Signature (XMLDIG) "Detached Signature" form.
   */
  data?: string;
  _data?: Element;
  /**
   * The party that can't sign. For example a child.
   */
  onBehalfOf?: Reference;
  /**
   * A mime type that indicates the technical format of the signature. Important mime types are application/signature+xml for X ML DigSig, application/jose for JWS, and image/* for a graphical image of a signature, etc.
   */
  sigFormat?: string;
  _sigFormat?: Element;
  /**
   * "xml", "json" and "ttl" are allowed, which describe the simple encodings described in the specification (and imply appropriate bundle support). Otherwise, mime types are legal here.
   */
  targetFormat?: string;
  _targetFormat?: Element;
  /**
   * Examples include attesting to: authorship, correct transcription, and witness of specific event. Also known as a &quot;Commitment Type Indication&quot;.
   */
  type: Coding[];
  /**
   * This should agree with the information in the signature.
   */
  when: string;
  _when?: Element;
  /**
   * This should agree with the information in the signature.
   */
  who: Reference;
}
/**
 * The comparator is not used on a SimpleQuantity
 */
export interface SimpleQuantity extends Quantity {
  /**
   * The preferred system is UCUM, but SNOMED CT can also be used (for customary units) or ISO 4217 for currency.  The context of use may additionally require a code from a particular system.
   */
  code?: string;
  _code?: Element;
  /**
   * The identification of the system that provides the coded form of the unit.
   */
  system?: string;
  _system?: Element;
  /**
   * A human-readable form of the unit.
   */
  unit?: string;
  _unit?: Element;
  /**
   * The implicit precision in the value should always be honored. Monetary values have their own rules for handling precision (refer to standard accounting text books).
   */
  value?: number;
}
/**
 * Reference range of possible or expected values.
 */
export interface SubstanceAmountReferenceRange extends Element {
  /**
   * Upper limit possible or expected.
   */
  highLimit?: Quantity;
  /**
   * Lower limit possible or expected.
   */
  lowLimit?: Quantity;
}
/**
 * Chemical substances are a single substance type whose primary defining element is the molecular structure. Chemical substances shall be defined on the basis of their complete covalent molecular structure; the presence of a salt (counter-ion) and/or solvates (water, alcohols) is also captured. Purity, grade, physical form or particle size are not taken into account in the definition of a chemical substance or in the assignment of a Substance ID.
 */
export interface SubstanceAmount extends BackboneElement {
  /**
   * Used to capture quantitative values for a variety of elements. If only limits are given, the arithmetic mean would be the average. If only a single definite value for a given element is given, it would be captured in this field.
   */
  amountQuantity?: Quantity;
  /**
   * Used to capture quantitative values for a variety of elements. If only limits are given, the arithmetic mean would be the average. If only a single definite value for a given element is given, it would be captured in this field.
   */
  amountRange?: Range;
  /**
   * Used to capture quantitative values for a variety of elements. If only limits are given, the arithmetic mean would be the average. If only a single definite value for a given element is given, it would be captured in this field.
   */
  amountString?: string;
  _amountString?: Element;
  /**
   * A textual comment on a numeric value.
   */
  amountText?: string;
  _amountText?: Element;
  /**
   * Most elements that require a quantitative value will also have a field called amount type. Amount type should always be specified because the actual value of the amount is often dependent on it. EXAMPLE: In capturing the actual relative amounts of substances or molecular fragments it is essential to indicate whether the amount refers to a mole ratio or weight ratio. For any given element an effort should be made to use same the amount type for all related definitional elements.
   */
  amountType?: CodeableConcept;
  /**
   * Reference range of possible or expected values.
   */
  referenceRange?: SubstanceAmountReferenceRange;
}
/**
 * A set of rules that describe when the event is scheduled.
 */
export interface TimingRepeat extends Element {
  /**
   * Either a duration for the length of the timing schedule, a range of possible length, or outer bounds for start and/or end limits of the timing schedule.
   */
  boundsDuration?: Duration;
  /**
   * Either a duration for the length of the timing schedule, a range of possible length, or outer bounds for start and/or end limits of the timing schedule.
   */
  boundsRange?: Range;
  /**
   * Either a duration for the length of the timing schedule, a range of possible length, or outer bounds for start and/or end limits of the timing schedule.
   */
  boundsPeriod?: Period;
  /**
   * If you have both bounds and count, then this should be understood as within the bounds period, until count times happens.
   */
  count?: number;
  /**
   * If present, indicates that the count is a range - so to perform the action between [count] and [countMax] times.
   */
  countMax?: number;
  /**
   * If no days are specified, the action is assumed to happen every day as otherwise specified. The elements frequency and period cannot be used as well as dayOfWeek.
   */
  dayOfWeek?: string[];
  _dayOfWeek?: Element;
  /**
   * For some events the duration is part of the definition of the event (e.g. IV infusions, where the duration is implicit in the specified quantity and rate). For others, it's part of the timing specification (e.g. exercise).
   */
  duration?: number;
  /**
   * For some events the duration is part of the definition of the event (e.g. IV infusions, where the duration is implicit in the specified quantity and rate). For others, it's part of the timing specification (e.g. exercise).
   */
  durationMax?: number;
  /**
   * The units of time for the duration, in UCUM units.
   */
  durationUnit?: string;
  _durationUnit?: Element;
  /**
   * The number of times to repeat the action within the specified period. If frequencyMax is present, this element indicates the lower bound of the allowed range of the frequency.
   */
  frequency?: number;
  /**
   * If present, indicates that the frequency is a range - so to repeat between [frequency] and [frequencyMax] times within the period or period range.
   */
  frequencyMax?: number;
  /**
   * The number of minutes from the event. If the event code does not indicate whether the minutes is before or after the event, then the offset is assumed to be after the event.
   */
  offset?: number;
  /**
   * Indicates the duration of time over which repetitions are to occur; e.g. to express "3 times per day", 3 would be the frequency and "1 day" would be the period. If periodMax is present, this element indicates the lower bound of the allowed range of the period length.
   */
  period?: number;
  /**
   * If present, indicates that the period is a range from [period] to [periodMax], allowing expressing concepts such as "do this once every 3-5 days.
   */
  periodMax?: number;
  /**
   * The units of time for the period in UCUM units.
   */
  periodUnit?: string;
  _periodUnit?: Element;
  /**
   * When time of day is specified, it is inferred that the action happens every day (as filtered by dayofWeek) on the specified times. The elements when, frequency and period cannot be used as well as timeOfDay.
   */
  timeOfDay?: string[];
  _timeOfDay?: Element;
  /**
   * When more than one event is listed, the event is tied to the union of the specified events.
   */
  when?: string[];
  _when?: Element;
}
/**
 * Code Values for the Timing.repeat.dayOfWeek field
 */
export enum TimingRepeatDayOfWeekCodes {
  MON = 'mon',
  TUE = 'tue',
  WED = 'wed',
  THU = 'thu',
  FRI = 'fri',
  SAT = 'sat',
  SUN = 'sun',
}
/**
 * Code Values for the Timing.repeat.durationUnit field
 */
export enum TimingRepeatDurationUnitCodes {
  S = 's',
  MIN = 'min',
  H = 'h',
  D = 'd',
  WK = 'wk',
  MO = 'mo',
  A = 'a',
}
/**
 * Code Values for the Timing.repeat.periodUnit field
 */
export enum TimingRepeatPeriodUnitCodes {
  S = 's',
  MIN = 'min',
  H = 'h',
  D = 'd',
  WK = 'wk',
  MO = 'mo',
  A = 'a',
}
/**
 * Specifies an event that may occur multiple times. Timing schedules are used to record when things are planned, expected or requested to occur. The most common usage is in dosage instructions for medications. They are also used when planning care of various kinds, and may be used for reporting the schedule to which past regular activities were carried out.
 */
export interface Timing extends BackboneElement {
  /**
   * BID etc. are defined as 'at institutionally specified times'. For example, an institution may choose that BID is "always at 7am and 6pm".  If it is inappropriate for this choice to be made, the code BID should not be used. Instead, a distinct organization-specific code should be used in place of the HL7-defined BID code and/or a structured representation should be used (in this case, specifying the two event times).
   */
  code?: CodeableConcept;
  /**
   * Identifies specific times when the event occurs.
   */
  event?: string[];
  _event?: Element;
  /**
   * A set of rules that describe when the event is scheduled.
   */
  repeat?: TimingRepeat;
}
/**
 * A description of a triggering event. Triggering events can be named events, data events, or periodic, as determined by the type element.
 */
export interface TriggerDefinition extends Element {
  /**
   * This element can be only be specified for data type triggers and provides additional semantics for the trigger. The context available within the condition is based on the type of data event. For all events, the current resource will be available as context. In addition, for modification events, the previous resource will also be available. The expression may be inlined, or may be a simple absolute URI, which is a reference to a named expression within a logic library referenced by a library element or extension within the containing resource. If the expression is a FHIR Path expression, it evaluates in the context of a resource of one of the type identified in the data requirement, and may also refer to the variable %previous for delta comparisons on events of type data-changed, data-modified, and data-deleted which will always have the same type.
   */
  condition?: Expression;
  /**
   * This element shall be present for any data type trigger.
   */
  data?: DataRequirement[];
  /**
   * An event name can be provided for all event types, but is required for named events. If a name is provided for a type other than named events, it is considered to be a shorthand for the semantics described by the formal description of the event.
   */
  name?: string;
  _name?: Element;
  /**
   * The timing of the event (if this is a periodic trigger).
   */
  timingTiming?: Timing;
  /**
   * The timing of the event (if this is a periodic trigger).
   */
  timingReference?: Reference;
  /**
   * The timing of the event (if this is a periodic trigger).
   */
  timingDate?: string;
  _timingDate?: Element;
  /**
   * The timing of the event (if this is a periodic trigger).
   */
  timingDateTime?: string;
  _timingDateTime?: Element;
  /**
   * The type of triggering event.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the TriggerDefinition.type field
 */
export enum TriggerDefinitionTypeCodes {
  NAMED_EVENT = 'named-event',
  PERIODIC = 'periodic',
  DATA_CHANGED = 'data-changed',
  DATA_ADDED = 'data-added',
  DATA_MODIFIED = 'data-modified',
  DATA_REMOVED = 'data-removed',
  DATA_ACCESSED = 'data-accessed',
  DATA_ACCESS_ENDED = 'data-access-ended',
}
/**
 * Specifies clinical/business/etc. metadata that can be used to retrieve, index and/or categorize an artifact. This metadata can either be specific to the applicable population (e.g., age category, DRG) or the specific context of care (e.g., venue, care setting, provider of care).
 */
export interface UsageContext extends Element {
  /**
   * A code that identifies the type of context being specified by this usage context.
   */
  code: Coding;
  /**
   * A value that defines the context specified in this context of use. The interpretation of the value is defined by the code.
   */
  valueCodeableConcept?: CodeableConcept;
  /**
   * A value that defines the context specified in this context of use. The interpretation of the value is defined by the code.
   */
  valueQuantity?: Quantity;
  /**
   * A value that defines the context specified in this context of use. The interpretation of the value is defined by the code.
   */
  valueRange?: Range;
  /**
   * A value that defines the context specified in this context of use. The interpretation of the value is defined by the code.
   */
  valueReferenc?: Reference;
}
/**
 * Typically. this may be some form of insurance, internal charges, or self-pay.
 * Local or jurisdictional business rules may determine which coverage covers which types of billable items charged to the account, and in which order.
 * Where the order is important, a local/jurisdictional extension may be defined to specify the order for the type of charge.
 */
export interface AccountCoverage extends BackboneElement {
  /**
   * The party(s) that contribute to payment (or part of) of the charges applied to this account (including self-pay).
   * A coverage may only be responsible for specific types of charges, and the sequence of the coverages in the account could be important when processing billing.
   */
  coverage: Reference;
  /**
   * It is common in some jurisdictions for there to be multiple coverages allocated to an account, and a sequence is required to order the settling of the account (often with insurance claiming).
   */
  priority?: number;
}
/**
 * The parties responsible for balancing the account if other payment options fall short.
 */
export interface AccountGuarantor extends BackboneElement {
  /**
   * A guarantor may be placed on credit hold or otherwise have their role temporarily suspended.
   */
  onHold?: boolean;
  _onHold?: Element;
  /**
   * The entity who is responsible.
   */
  party: Reference;
  /**
   * The timeframe during which the guarantor accepts responsibility for the account.
   */
  period?: Period;
}
/**
 * A financial tool for tracking value accrued for a particular purpose.  In the healthcare field, used to track charges for a patient, cost centers, etc.
 */
export interface Account extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Account';
  /**
   * Typically. this may be some form of insurance, internal charges, or self-pay.
   * Local or jurisdictional business rules may determine which coverage covers which types of billable items charged to the account, and in which order.
   * Where the order is important, a local/jurisdictional extension may be defined to specify the order for the type of charge.
   */
  coverage?: AccountCoverage[];
  /**
   * Provides additional information about what the account tracks and how it is used.
   */
  description?: string;
  _description?: Element;
  /**
   * The parties responsible for balancing the account if other payment options fall short.
   */
  guarantor?: AccountGuarantor[];
  /**
   * Unique identifier used to reference the account.  Might or might not be intended for human use (e.g. credit card number).
   */
  identifier?: Identifier[];
  /**
   * Name used for the account when displaying it to humans in reports, etc.
   */
  name?: string;
  _name?: Element;
  /**
   * Indicates the service area, hospital, department, etc. with responsibility for managing the Account.
   */
  owner?: Reference;
  /**
   * Reference to a parent Account.
   */
  partOf?: Reference;
  /**
   * It is possible for transactions to be posted outside the service period, as long as the service was provided within the defined service period.
   */
  servicePeriod?: Period;
  /**
   * This element is labeled as a modifier because the status contains the codes inactive and entered-in-error that mark the Account as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * Accounts can be applied to non-patients for tracking other non-patient related activities, such as group services (patients not tracked, and costs charged to another body), or might not be allocated.
   */
  subject?: Reference[];
  /**
   * Categorizes the account for reporting and searching purposes.
   */
  type?: CodeableConcept;
}
/**
 * Code Values for the Account.status field
 */
export enum AccountStatusCodes {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ENTERED_IN_ERROR = 'entered-in-error',
  ON_HOLD = 'on-hold',
  UNKNOWN = 'unknown',
}
/**
 * Indicates who should participate in performing the action described.
 */
export interface ActivityDefinitionParticipant extends BackboneElement {
  /**
   * The role the participant should play in performing the described action.
   */
  role?: CodeableConcept;
  /**
   * The type of participant in the action.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the ActivityDefinition.participant.type field
 */
export enum ActivityDefinitionParticipantTypeCodes {
  PATIENT = 'patient',
  PRACTITIONER = 'practitioner',
  RELATED_PERSON = 'related-person',
  DEVICE = 'device',
}
/**
 * Dynamic values are applied in the order in which they are defined in the ActivityDefinition. Note that if both a transform and dynamic values are specified, the dynamic values will be applied to the result of the transform.
 */
export interface ActivityDefinitionDynamicValue extends BackboneElement {
  /**
   * The expression may be inlined, or may be a reference to a named expression within a logic library referenced by the library element.
   */
  expression: Expression;
  /**
   * The path attribute contains a [Simple FHIRPath Subset](fhirpath.html#simple) that allows path traversal, but not calculation.
   */
  path: string;
  _path?: Element;
}
/**
 * This resource allows for the definition of some activity to be performed, independent of a particular patient, practitioner, or other performance context.
 */
export interface ActivityDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ActivityDefinition';
  /**
   * The 'date' element may be more recent than the approval date because of minor changes or editorial corrections.
   */
  approvalDate?: string;
  _approvalDate?: Element;
  /**
   * An individiual or organization primarily involved in the creation and maintenance of the content.
   */
  author?: ContactDetail[];
  /**
   * Only used if not implicit in the code found in ServiceRequest.type.
   */
  bodySite?: CodeableConcept[];
  /**
   * Tends to be less relevant for activities involving particular products.
   */
  code?: CodeableConcept;
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the activity definition and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the activity definition.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the activity definition. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the activity definition was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the activity definition as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the activity definition is presumed to be the predominant language in the place the activity definition was created).
   */
  description?: string;
  _description?: Element;
  /**
   * This element is not intended to be used to communicate a decision support response to cancel an order in progress. That should be done with the "remove" type of a PlanDefinition or RequestGroup.
   */
  doNotPerform?: boolean;
  _doNotPerform?: Element;
  /**
   * If a dosage instruction is used, the definition should not specify timing or quantity.
   */
  dosage?: Dosage[];
  /**
   * Dynamic values are applied in the order in which they are defined in the ActivityDefinition. Note that if both a transform and dynamic values are specified, the dynamic values will be applied to the result of the transform.
   */
  dynamicValue?: ActivityDefinitionDynamicValue[];
  /**
   * An individual or organization primarily responsible for internal coherence of the content.
   */
  editor?: ContactDetail[];
  /**
   * The effective period for a activity definition  determines when the content is applicable for usage and is independent of publication and review dates. For example, a measure intended to be used for the year 2016 might be published in 2015.
   */
  effectivePeriod?: Period;
  /**
   * An individual or organization responsible for officially endorsing the content for use in some setting.
   */
  endorser?: ContactDetail[];
  /**
   * Allows filtering of activity definitions that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this activity definition outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * Indicates the level of authority/intentionality associated with the activity and where the request should fit into the workflow chain.
   */
  intent?: string;
  _intent?: Element;
  /**
   * It may be possible for the activity definition to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * May determine what types of extensions are permitted.
   */
  kind?: string;
  _kind?: Element;
  /**
   * If specified, this date follows the original approval date.
   */
  lastReviewDate?: string;
  _lastReviewDate?: Element;
  /**
   * A reference to a Library resource containing any formal logic used by the activity definition.
   */
  library?: string[];
  _library?: Element;
  /**
   * May reference a specific clinical location or may just identify a type of location.
   */
  location?: Reference;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * Defines observation requirements for the action to be performed, such as body weight or surface area.
   */
  observationRequirement?: Reference[];
  /**
   * Defines the observations that are expected to be produced by the action.
   */
  observationResultRequirement?: Reference[];
  /**
   * Indicates who should participate in performing the action described.
   */
  participant?: ActivityDefinitionParticipant[];
  /**
   * Indicates how quickly the activity  should be addressed with respect to other requests.
   */
  priority?: string;
  _priority?: Element;
  /**
   * Identifies the food, drug or other product being consumed or supplied in the activity.
   */
  productReference?: Reference;
  /**
   * Identifies the food, drug or other product being consumed or supplied in the activity.
   */
  productCodeableConcept?: CodeableConcept;
  /**
   * A profile to which the target of the activity definition is expected to conform.
   */
  profile?: string;
  _profile?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the activity definition is the organization or individual primarily responsible for the maintenance and upkeep of the activity definition. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the activity definition. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the activity definition. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this activity definition.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Identifies the quantity expected to be consumed at once (per dose, per meal, etc.).
   */
  quantity?: Quantity;
  /**
   * Each related artifact is either an attachment, or a reference to another resource, but not both.
   */
  relatedArtifact?: RelatedArtifact[];
  /**
   * An individual or organization primarily responsible for review of some aspect of the content.
   */
  reviewer?: ContactDetail[];
  /**
   * Defines specimen requirements for the action to be performed, such as required specimens for a lab test.
   */
  specimenRequirement?: Reference[];
  /**
   * Allows filtering of activity definitions that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * A code or group definition that describes the intended subject of the activity being defined.
   */
  subjectCodeableConcept?: CodeableConcept;
  /**
   * A code or group definition that describes the intended subject of the activity being defined.
   */
  subjectReference?: Reference;
  /**
   * An explanatory or alternate title for the activity definition giving additional information about its content.
   */
  subtitle?: string;
  _subtitle?: Element;
  /**
   * The period, timing or frequency upon which the described activity is to occur.
   */
  timingTiming?: Timing;
  /**
   * The period, timing or frequency upon which the described activity is to occur.
   */
  timingDateTime?: string;
  _timingDateTime?: Element;
  /**
   * The period, timing or frequency upon which the described activity is to occur.
   */
  timingAge?: Age;
  /**
   * The period, timing or frequency upon which the described activity is to occur.
   */
  timingPeriod?: Period;
  /**
   * The period, timing or frequency upon which the described activity is to occur.
   */
  timingRange?: Range;
  /**
   * The period, timing or frequency upon which the described activity is to occur.
   */
  timingDuration?: Duration;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Descriptive topics related to the content of the activity. Topics provide a high-level categorization of the activity that can be useful for filtering and searching.
   */
  topic?: CodeableConcept[];
  /**
   * Note that if both a transform and dynamic values are specified, the dynamic values will be applied to the result of the transform.
   */
  transform?: string;
  _transform?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * A detailed description of how the activity definition is used from a clinical perspective.
   */
  usage?: string;
  _usage?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different activity definition instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the activity definition with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the ActivityDefinition.intent field
 */
export enum ActivityDefinitionIntentCodes {
  PROPOSAL = 'proposal',
  PLAN = 'plan',
  DIRECTIVE = 'directive',
  ORDER = 'order',
  ORIGINAL_ORDER = 'original-order',
  REFLEX_ORDER = 'reflex-order',
  FILLER_ORDER = 'filler-order',
  INSTANCE_ORDER = 'instance-order',
  OPTION = 'option',
}
/**
 * Code Values for the ActivityDefinition.priority field
 */
export enum ActivityDefinitionPriorityCodes {
  ROUTINE = 'routine',
  URGENT = 'urgent',
  ASAP = 'asap',
  STAT = 'stat',
}
/**
 * Code Values for the ActivityDefinition.status field
 */
export enum ActivityDefinitionStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Information on the possible cause of the event.
 */
export interface AdverseEventSuspectEntityCausality extends BackboneElement {
  /**
   * Assessment of if the entity caused the event.
   */
  assessment?: CodeableConcept;
  /**
   * AdverseEvent.suspectEntity.causalityAuthor.
   */
  author?: Reference;
  /**
   * ProbabilityScale | Bayesian | Checklist.
   */
  method?: CodeableConcept;
  /**
   * AdverseEvent.suspectEntity.causalityProductRelatedness.
   */
  productRelatedness?: string;
  _productRelatedness?: Element;
}
/**
 * Describes the entity that is suspected to have caused the adverse event.
 */
export interface AdverseEventSuspectEntity extends BackboneElement {
  /**
   * Information on the possible cause of the event.
   */
  causality?: AdverseEventSuspectEntityCausality[];
  /**
   * Identifies the actual instance of what caused the adverse event.  May be a substance, medication, medication administration, medication statement or a device.
   */
  instance: Reference;
}
/**
 * Actual or  potential/avoided event causing unintended physical injury resulting from or contributed to by medical care, a research study or other healthcare setting factors that requires additional monitoring, treatment, or hospitalization, or that results in death.
 */
export interface AdverseEvent extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'AdverseEvent';
  /**
   * Whether the event actually happened, or just had the potential to. Note that this is independent of whether anyone was affected or harmed or how severely.
   */
  actuality: string;
  _actuality?: Element;
  /**
   * The overall type of event, intended for search and filtering purposes.
   */
  category?: CodeableConcept[];
  /**
   * Parties that may or should contribute or have contributed information to the adverse event, which can consist of one or more activities.  Such information includes information leading to the decision to perform the activity and how to perform the activity (e.g. consultant), information that the activity itself seeks to reveal (e.g. informant of clinical history), or information about what activity was performed (e.g. informant witness).
   */
  contributor?: Reference[];
  /**
   * The date (and perhaps time) when the adverse event occurred.
   */
  date?: string;
  _date?: Element;
  /**
   * Estimated or actual date the AdverseEvent began, in the opinion of the reporter.
   */
  detected?: string;
  _detected?: Element;
  /**
   * This will typically be the encounter the event occurred within, but some activities may be initiated prior to or after the official completion of an encounter but still be tied to the context of the encounter.  For example, if a medication administration was considered an adverse event because it resulted in a rash, then the encounter when the medication administration was given is the context.  If the patient reports the AdverseEvent during a second encounter, that second encounter is not the context.
   */
  encounter?: Reference;
  /**
   * This element defines the specific type of event that occurred or that was prevented from occurring.
   */
  event?: CodeableConcept;
  /**
   * This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  identifier?: Identifier;
  /**
   * The information about where the adverse event occurred.
   */
  location?: Reference;
  /**
   * Describes the type of outcome from the adverse event.
   */
  outcome?: CodeableConcept;
  /**
   * The recordedDate represents the date when this particular AdverseEvent record was created in the system, not the date of the most recent update.  The date of the last record modification can be retrieved from the resource metadata.
   */
  recordedDate?: string;
  _recordedDate?: Element;
  /**
   * Information on who recorded the adverse event.  May be the patient or a practitioner.
   */
  recorder?: Reference;
  /**
   * AdverseEvent.referenceDocument.
   */
  referenceDocument?: Reference[];
  /**
   * Includes information about the reaction that occurred as a result of exposure to a substance (for example, a drug or a chemical).
   */
  resultingCondition?: Reference[];
  /**
   * Assessment whether this event was of real importance.
   */
  seriousness?: CodeableConcept;
  /**
   * Describes the severity of the adverse event, in relation to the subject. Contrast to AdverseEvent.seriousness - a severe rash might not be serious, but a mild heart problem is.
   */
  severity?: CodeableConcept;
  /**
   * AdverseEvent.study.
   */
  study?: Reference[];
  /**
   * If AdverseEvent.resultingCondition differs among members of the group, then use Patient as the subject.
   */
  subject: Reference;
  /**
   * AdverseEvent.subjectMedicalHistory.
   */
  subjectMedicalHistory?: Reference[];
  /**
   * Describes the entity that is suspected to have caused the adverse event.
   */
  suspectEntity?: AdverseEventSuspectEntity[];
}
/**
 * Code Values for the AdverseEvent.actuality field
 */
export enum AdverseEventActualityCodes {
  ACTUAL = 'actual',
  POTENTIAL = 'potential',
}
/**
 * Details about each adverse reaction event linked to exposure to the identified substance.
 */
export interface AllergyIntoleranceReaction extends BackboneElement {
  /**
   * Use the description to provide any details of a particular event of the occurred reaction such as circumstances, reaction specifics, what happened before/after. Information, related to the event, but not describing a particular care should be captured in the comment field. For example: at the age of four, the patient was given penicillin for strep throat and subsequently developed severe hives.
   */
  description?: string;
  _description?: Element;
  /**
   * Coding of the route of exposure with a terminology should be used wherever possible.
   */
  exposureRoute?: CodeableConcept;
  /**
   * Manifestation can be expressed as a single word, phrase or brief description. For example: nausea, rash or no reaction. It is preferable that manifestation should be coded with a terminology, where possible. The values entered here may be used to display on an application screen as part of a list of adverse reactions, as recommended in the UK NHS CUI guidelines.  Terminologies commonly used include, but are not limited to, SNOMED CT or ICD10.
   */
  manifestation: CodeableConcept[];
  /**
   * Use this field to record information indirectly related to a particular event and not captured in the description. For example: Clinical records are no longer available, recorded based on information provided to the patient by her mother and her mother is deceased.
   */
  note?: Annotation[];
  /**
   * Record of the date and/or time of the onset of the Reaction.
   */
  onset?: string;
  _onset?: Element;
  /**
   * It is acknowledged that this assessment is very subjective. There may be some specific practice domains where objective scales have been applied. Objective scales can be included in this model as extensions.
   */
  severity?: string;
  _severity?: Element;
  /**
   * Coding of the specific substance (or pharmaceutical product) with a terminology capable of triggering decision support should be used wherever possible.  The 'code' element allows for the use of a specific substance or pharmaceutical product, or a group or class of substances. In the case of an allergy or intolerance to a class of substances, (for example, "penicillins"), the 'reaction.substance' element could be used to code the specific substance that was identified as having caused the reaction (for example, "amoxycillin"). Duplication of the value in the 'code' and 'reaction.substance' elements is acceptable when a specific substance has been recorded in 'code'.
   */
  substance?: CodeableConcept;
}
/**
 * Code Values for the AllergyIntolerance.reaction.severity field
 */
export enum AllergyIntoleranceReactionSeverityCodes {
  MILD = 'mild',
  MODERATE = 'moderate',
  SEVERE = 'severe',
}
/**
 * Risk of harmful or undesirable, physiological response which is unique to an individual and associated with exposure to a substance.
 */
export interface AllergyIntolerance extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'AllergyIntolerance';
  /**
   * The recorder takes responsibility for the content, but can reference the source from where they got it.
   */
  asserter?: Reference;
  /**
   * This data element has been included because it is currently being captured in some clinical systems. This data can be derived from the substance where coding systems are used, and is effectively redundant in that situation.  When searching on category, consider the implications of AllergyIntolerance resources without a category.  For example, when searching on category = medication, medication allergies that don't have a category valued will not be returned.  Refer to [search](search.html) for more information on how to search category with a :missing modifier to get allergies that don't have a category.  Additionally, category should be used with caution because category can be subjective based on the sender.
   */
  category?: string[];
  _category?: Element;
  /**
   * Refer to [discussion](extensibility.html#Special-Case) if clincalStatus is missing data.
   * The data type is CodeableConcept because clinicalStatus has some clinical judgment involved, such that there might need to be more specificity than the required FHIR value set allows. For example, a SNOMED coding might allow for additional specificity.
   */
  clinicalStatus?: CodeableConcept;
  /**
   * It is strongly recommended that this element be populated using a terminology, where possible. For example, some terminologies used include RxNorm, SNOMED CT, DM+D, NDFRT, ICD-9, IDC-10, UNII, and ATC. Plain text should only be used if there is no appropriate terminology available. Additional details can be specified in the text.
   * When a substance or product code is specified for the 'code' element, the "default" semantic context is that this is a positive statement of an allergy or intolerance (depending on the value of the 'type' element, if present) condition to the specified substance/product.  In the corresponding SNOMED CT allergy model, the specified substance/product is the target (destination) of the "Causative agent" relationship.
   * The 'substanceExposureRisk' extension is available as a structured and more flexible alternative to the 'code' element for making positive or negative allergy or intolerance statements.  This extension provides the capability to make "no known allergy" (or "no risk of adverse reaction") statements regarding any coded substance/product (including cases when a pre-coordinated "no allergy to x" concept for that substance/product does not exist).  If the 'substanceExposureRisk' extension is present, the AllergyIntolerance.code element SHALL be omitted.
   */
  code?: CodeableConcept;
  /**
   * The default criticality value for any propensity to an adverse reaction should be 'Low Risk', indicating at the very least a relative contraindication to deliberate or voluntary exposure to the substance. 'High Risk' is flagged if the clinician has identified a propensity for a more serious or potentially life-threatening reaction, such as anaphylaxis, and implies an absolute contraindication to deliberate or voluntary exposure to the substance. If this element is missing, the criticality is unknown (though it may be known elsewhere).  Systems that capture a severity at the condition level are actually representing the concept of criticality whereas the severity documented at the reaction level is representing the true reaction severity.  Existing systems that are capturing both condition criticality and reaction severity may use the term "severity" to represent both.  Criticality is the worst it could be in the future (i.e. situation-agnostic) whereas severity is situation-dependent.
   */
  criticality?: string;
  _criticality?: Element;
  /**
   * The encounter when the allergy or intolerance was asserted.
   */
  encounter?: Reference;
  /**
   * This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  identifier?: Identifier[];
  /**
   * This date may be replicated by one of the Onset of Reaction dates. Where a textual representation of the date of last occurrence is required e.g. 'In Childhood, '10 years ago' the Comment element should be used.
   */
  lastOccurrence?: string;
  _lastOccurrence?: Element;
  /**
   * For example: including reason for flagging a seriousness of 'High Risk'; and instructions related to future exposure or administration of the substance, such as administration within an Intensive Care Unit or under corticosteroid cover. The notes should be related to an allergy or intolerance as a condition in general and not related to any particular episode of it. For episode notes and descriptions, use AllergyIntolerance.event.description and  AllergyIntolerance.event.notes.
   */
  note?: Annotation[];
  /**
   * Estimated or actual date,  date-time, or age when allergy or intolerance was identified.
   */
  onsetDateTime?: string;
  _onsetDateTime?: Element;
  /**
   * Estimated or actual date,  date-time, or age when allergy or intolerance was identified.
   */
  onsetAge?: Age;
  /**
   * Estimated or actual date,  date-time, or age when allergy or intolerance was identified.
   */
  onsetPeriod?: Period;
  /**
   * Estimated or actual date,  date-time, or age when allergy or intolerance was identified.
   */
  onsetRange?: Range;
  /**
   * Estimated or actual date,  date-time, or age when allergy or intolerance was identified.
   */
  onsetString?: string;
  _onsetString?: Element;
  /**
   * The patient who has the allergy or intolerance.
   */
  patient: Reference;
  /**
   * Details about each adverse reaction event linked to exposure to the identified substance.
   */
  reaction?: AllergyIntoleranceReaction[];
  /**
   * The recordedDate represents when this particular AllergyIntolerance record was created in the system, which is often a system-generated date.
   */
  recordedDate?: string;
  _recordedDate?: Element;
  /**
   * Individual who recorded the record and takes responsibility for its content.
   */
  recorder?: Reference;
  /**
   * Allergic (typically immune-mediated) reactions have been traditionally regarded as an indicator for potential escalation to significant future risk. Contemporary knowledge suggests that some reactions previously thought to be immune-mediated are, in fact, non-immune, but in some cases can still pose a life threatening risk. It is acknowledged that many clinicians might not be in a position to distinguish the mechanism of a particular reaction. Often the term "allergy" is used rather generically and may overlap with the use of "intolerance" - in practice the boundaries between these two concepts might not be well-defined or understood. This data element is included nevertheless, because many legacy systems have captured this attribute. Immunologic testing may provide supporting evidence for the basis of the reaction and the causative substance, but no tests are 100% sensitive or specific for sensitivity to a particular substance. If, as is commonly the case, it is unclear whether the reaction is due to an allergy or an intolerance, then the type element should be omitted from the resource.
   */
  type?: string;
  _type?: Element;
  /**
   * The data type is CodeableConcept because verificationStatus has some clinical judgment involved, such that there might need to be more specificity than the required FHIR value set allows. For example, a SNOMED coding might allow for additional specificity.
   */
  verificationStatus?: CodeableConcept;
}
/**
 * Code Values for the AllergyIntolerance.category field
 */
export enum AllergyIntoleranceCategoryCodes {
  FOOD = 'food',
  MEDICATION = 'medication',
  ENVIRONMENT = 'environment',
  BIOLOGIC = 'biologic',
}
/**
 * Code Values for the AllergyIntolerance.criticality field
 */
export enum AllergyIntoleranceCriticalityCodes {
  LOW = 'low',
  HIGH = 'high',
  UNABLE_TO_ASSESS = 'unable-to-assess',
}
/**
 * Code Values for the AllergyIntolerance.type field
 */
export enum AllergyIntoleranceTypeCodes {
  ALLERGY = 'allergy',
  INTOLERANCE = 'intolerance',
}
/**
 * List of participants involved in the appointment.
 */
export interface AppointmentParticipant extends BackboneElement {
  /**
   * A Person, Location/HealthcareService or Device that is participating in the appointment.
   */
  actor?: Reference;
  /**
   * Participation period of the actor.
   */
  period?: Period;
  /**
   * Whether this participant is required to be present at the meeting. This covers a use-case where two doctors need to meet to discuss the results for a specific patient, and the patient is not required to be present.
   */
  required?: string;
  _required?: Element;
  /**
   * Participation status of the actor.
   */
  status: string;
  _status?: Element;
  /**
   * The role of the participant can be used to declare what the actor will be doing in the scope of this appointment.
   * If the actor is not specified, then it is expected that the actor will be filled in at a later stage of planning.
   * This value SHALL be the same when creating an AppointmentResponse so that they can be matched, and subsequently update the Appointment.
   */
  type?: CodeableConcept[];
}
/**
 * Code Values for the Appointment.participant.required field
 */
export enum AppointmentParticipantRequiredCodes {
  REQUIRED = 'required',
  OPTIONAL = 'optional',
  INFORMATION_ONLY = 'information-only',
}
/**
 * Code Values for the Appointment.participant.status field
 */
export enum AppointmentParticipantStatusCodes {
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  TENTATIVE = 'tentative',
  NEEDS_ACTION = 'needs-action',
}
/**
 * A booking of a healthcare event among patient(s), practitioner(s), related person(s) and/or device(s) for a specific date/time. This may result in one or more Encounter(s).
 */
export interface Appointment extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Appointment';
  /**
   * The style of appointment or patient that has been booked in the slot (not service type).
   */
  appointmentType?: CodeableConcept;
  /**
   * The service request this appointment is allocated to assess (e.g. incoming referral or procedure request).
   */
  basedOn?: Reference[];
  /**
   * The coded reason for the appointment being cancelled. This is often used in reporting/billing/futher processing to determine if further actions are required, or specific fees apply.
   */
  cancelationReason?: CodeableConcept;
  /**
   * Additional text to aid in facilitating the appointment. For instance, a comment might be, "patient should proceed immediately to infusion room upon arrival"
   * Where this is a planned appointment and the start/end dates are not set then this field can be used to provide additional guidance on the details of the appointment request, including any restrictions on when to book it.
   */
  comment?: string;
  _comment?: Element;
  /**
   * This property is required for many use cases where the age of an appointment is considered in processing workflows for scheduling and billing of appointments.
   */
  created?: string;
  _created?: Element;
  /**
   * The brief description of the appointment as would be shown on a subject line in a meeting request, or appointment list. Detailed or expanded information should be put in the comment field.
   */
  description?: string;
  _description?: Element;
  /**
   * Date/Time that the appointment is to conclude.
   */
  end?: string;
  _end?: Element;
  /**
   * This records identifiers associated with this appointment concern that are defined by business processes and/or used to refer to it when a direct URL reference to the resource itself is not appropriate (e.g. in CDA documents, or in written / printed documentation).
   */
  identifier?: Identifier[];
  /**
   * Number of minutes that the appointment is to take. This can be less than the duration between the start and end times.  For example, where the actual time of appointment is only an estimate or if a 30 minute appointment is being requested, but any time would work.  Also, if there is, for example, a planned 15 minute break in the middle of a long appointment, the duration may be 15 minutes less than the difference between the start and end.
   */
  minutesDuration?: number;
  /**
   * List of participants involved in the appointment.
   */
  participant: AppointmentParticipant[];
  /**
   * While Appointment.comment contains information for internal use, Appointment.patientInstructions is used to capture patient facing information about the Appointment (e.g. please bring your referral or fast from 8pm night before).
   */
  patientInstruction?: string;
  _patientInstruction?: Element;
  /**
   * Seeking implementer feedback on this property and how interoperable it is.
   * Using an extension to record a CodeableConcept for named values may be tested at a future connectathon.
   */
  priority?: number;
  /**
   * The coded reason that this appointment is being scheduled. This is more clinical than administrative.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Reason the appointment has been scheduled to take place, as specified using information from another resource. When the patient arrives and the encounter begins it may be used as the admission diagnosis. The indication will typically be a Condition (with other resources referenced in the evidence.detail), or a Procedure.
   */
  reasonReference?: Reference[];
  /**
   * This does not introduce a capacity for recurring appointments.
   */
  requestedPeriod?: Period[];
  /**
   * A broad categorization of the service that is to be performed during this appointment.
   */
  serviceCategory?: CodeableConcept[];
  /**
   * For a provider to provider appointment the code "FOLLOWUP" may be appropriate, as this is expected to be discussing some patient that was seen in the past.
   */
  serviceType?: CodeableConcept[];
  /**
   * The slots from the participants' schedules that will be filled by the appointment.
   */
  slot?: Reference[];
  /**
   * The specialty of a practitioner that would be required to perform the service requested in this appointment.
   */
  specialty?: CodeableConcept[];
  /**
   * Date/Time that the appointment is to take place.
   */
  start?: string;
  _start?: Element;
  /**
   * If the Appointment's status is "cancelled" then all participants are expected to have their calendars released for the appointment period, and as such any Slots that were marked as BUSY can be re-set to FREE.
   * This element is labeled as a modifier because the status contains the code entered-in-error that mark the Appointment as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * Additional information to support the appointment provided when making the appointment.
   */
  supportingInformation?: Reference[];
}
/**
 * Code Values for the Appointment.status field
 */
export enum AppointmentStatusCodes {
  PROPOSED = 'proposed',
  PENDING = 'pending',
  BOOKED = 'booked',
  ARRIVED = 'arrived',
  FULFILLED = 'fulfilled',
  CANCELLED = 'cancelled',
  NOSHOW = 'noshow',
  ENTERED_IN_ERROR = 'entered-in-error',
  CHECKED_IN = 'checked-in',
  WAITLIST = 'waitlist',
}
/**
 * A reply to an appointment request for a patient and/or practitioner(s), such as a confirmation or rejection.
 */
export interface AppointmentResponse extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'AppointmentResponse';
  /**
   * A Person, Location, HealthcareService, or Device that is participating in the appointment.
   */
  actor?: Reference;
  /**
   * Appointment that this response is replying to.
   */
  appointment: Reference;
  /**
   * This comment is particularly important when the responder is declining, tentatively accepting or requesting another time to indicate the reasons why.
   */
  comment?: string;
  _comment?: Element;
  /**
   * This may be either the same as the appointment request to confirm the details of the appointment, or alternately a new time to request a re-negotiation of the end time.
   */
  end?: string;
  _end?: Element;
  /**
   * This records identifiers associated with this appointment response concern that are defined by business processes and/ or used to refer to it when a direct URL reference to the resource itself is not appropriate.
   */
  identifier?: Identifier[];
  /**
   * This element is labeled as a modifier because the status contains the code entered-in-error that marks the participant as not currently valid.
   */
  participantStatus: string;
  _participantStatus?: Element;
  /**
   * The role of the participant can be used to declare what the actor will be doing in the scope of the referenced appointment.
   * If the actor is not specified, then it is expected that the actor will be filled in at a later stage of planning.
   * This value SHALL be the same as specified on the referenced Appointment so that they can be matched, and subsequently updated.
   */
  participantType?: CodeableConcept[];
  /**
   * This may be either the same as the appointment request to confirm the details of the appointment, or alternately a new time to request a re-negotiation of the start time.
   */
  start?: string;
  _start?: Element;
}
/**
 * Code Values for the AppointmentResponse.participantStatus field
 */
export enum AppointmentResponseParticipantStatusCodes {
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  TENTATIVE = 'tentative',
  NEEDS_ACTION = 'needs-action',
}
/**
 * Logical network location for application activity, if the activity has a network location.
 */
export interface AuditEventAgentNetwork extends BackboneElement {
  /**
   * This could be a device id, IP address or some other identifier associated with a device.
   */
  address?: string;
  _address?: Element;
  /**
   * An identifier for the type of network access point that originated the audit event.
   */
  type?: string;
  _type?: Element;
}
/**
 * Several agents may be associated (i.e. have some responsibility for an activity) with an event or activity.
 * For example, an activity may be initiated by one user for other users or involve more than one user. However, only one user may be the initiator/requestor for the activity.
 */
export interface AuditEventAgent extends BackboneElement {
  /**
   * Alternative agent Identifier. For a human, this should be a user identifier text string from authentication system. This identifier would be one known to a common authentication system (e.g. single sign-on), if available.
   */
  altId?: string;
  _altId?: Element;
  /**
   * Where the event occurred.
   */
  location?: Reference;
  /**
   * Type of media involved. Used when the event is about exporting/importing onto media.
   */
  media?: Coding;
  /**
   * Human-meaningful name for the agent.
   */
  name?: string;
  _name?: Element;
  /**
   * Logical network location for application activity, if the activity has a network location.
   */
  network?: AuditEventAgentNetwork;
  /**
   * For example: Where an OAuth token authorizes, the unique identifier from the OAuth token is placed into the policy element Where a policy engine (e.g. XACML) holds policy logic, the unique policy identifier is placed into the policy element.
   */
  policy?: string[];
  _policy?: Element;
  /**
   * Use AuditEvent.agent.purposeOfUse when you know that is specific to the agent, otherwise use AuditEvent.purposeOfEvent. For example, during a machine-to-machine transfer it might not be obvious to the audit system who caused the event, but it does know why.
   */
  purposeOfUse?: CodeableConcept[];
  /**
   * There can only be one initiator. If the initiator is not clear, then do not choose any one agent as the initiator.
   */
  requestor: boolean;
  _requestor?: Element;
  /**
   * Should be roles relevant to the event. Should  not be an exhaustive list of roles.
   */
  role?: CodeableConcept[];
  /**
   * Specification of the participation type the user plays when performing the event.
   */
  type?: CodeableConcept;
  /**
   * Where a User ID is available it will go into who.identifier.
   */
  who?: Reference;
}
/**
 * Since multi-tier, distributed, or composite applications make source identification ambiguous, this collection of fields may repeat for each application or process actively involved in the event. For example, multiple value-sets can identify participating web servers, application processes, and database server threads in an n-tier distributed application. Passive event participants (e.g. low-level network transports) need not be identified.
 */
export interface AuditEventSource extends BackboneElement {
  /**
   * Identifier of the source where the event was detected.
   */
  observer: Reference;
  /**
   * Logical source location within the healthcare enterprise network.  For example, a hospital or other provider location within a multi-entity provider group.
   */
  site?: string;
  _site?: Element;
  /**
   * Code specifying the type of source where event originated.
   */
  type?: Coding[];
}
/**
 * Tagged value pairs for conveying additional information about the entity.
 */
export interface AuditEventEntityDetail extends BackboneElement {
  /**
   * The type of extra detail provided in the value.
   */
  type: string;
  _type?: Element;
  /**
   * The value can be string when known to be a string, else base64 encoding should be used to protect binary or undefined content.  The meaning and secondary-encoding of the content of base64 encoded blob is specific to the AuditEvent.type, AuditEvent.subtype, AuditEvent.entity.type, and AuditEvent.entity.role.  The base64 is a general-use and safe container for event specific data blobs regardless of the encoding used by the transaction being recorded.  An AuditEvent consuming application must understand the event it is consuming and the formats used by the event. For example if auditing an Oracle network database access, the Oracle formats must be understood as they will be simply encoded in the base64binary blob.
   */
  valueString: string;
  _valueString?: Element;
  /**
   * The value can be string when known to be a string, else base64 encoding should be used to protect binary or undefined content.  The meaning and secondary-encoding of the content of base64 encoded blob is specific to the AuditEvent.type, AuditEvent.subtype, AuditEvent.entity.type, and AuditEvent.entity.role.  The base64 is a general-use and safe container for event specific data blobs regardless of the encoding used by the transaction being recorded.  An AuditEvent consuming application must understand the event it is consuming and the formats used by the event. For example if auditing an Oracle network database access, the Oracle formats must be understood as they will be simply encoded in the base64binary blob.
   */
  valueBase64Binary: string;
  _valueBase64Binary?: Element;
}
/**
 * Required unless the values for event identification, agent identification, and audit source identification are sufficient to document the entire auditable event. Because events may have more than one entity, this group can be a repeating set of values.
 */
export interface AuditEventEntity extends BackboneElement {
  /**
   * Text that describes the entity in more detail.
   */
  description?: string;
  _description?: Element;
  /**
   * Tagged value pairs for conveying additional information about the entity.
   */
  detail?: AuditEventEntityDetail[];
  /**
   * This can be used to provide an audit trail for data, over time, as it passes through the system.
   */
  lifecycle?: Coding;
  /**
   * This field may be used in a query/report to identify audit events for a specific person.  For example, where multiple synonymous entity identifiers (patient number, medical record number, encounter number, etc.) have been used.
   */
  name?: string;
  _name?: Element;
  /**
   * The meaning and secondary-encoding of the content of base64 encoded blob is specific to the AuditEvent.type, AuditEvent.subtype, AuditEvent.entity.type, and AuditEvent.entity.role.  The base64 is a general-use and safe container for event specific data blobs regardless of the encoding used by the transaction being recorded.  An AuditEvent consuming application must understand the event it is consuming and the formats used by the event. For example, if auditing an Oracle network database access, the Oracle formats must be understood as they will be simply encoded in the base64binary blob.
   */
  query?: string;
  _query?: Element;
  /**
   * Code representing the role the entity played in the event being audited.
   */
  role?: Coding;
  /**
   * Copied from entity meta security tags.
   */
  securityLabel?: Coding[];
  /**
   * This value is distinct from the user's role or any user relationship to the entity.
   */
  type?: Coding;
  /**
   * Identifies a specific instance of the entity. The reference should be version specific.
   */
  what?: Reference;
}
/**
 * A record of an event made for purposes of maintaining a security log. Typical uses include detection of intrusion attempts and monitoring for inappropriate usage.
 */
export interface AuditEvent extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'AuditEvent';
  /**
   * Indicator for type of action performed during the event that generated the audit.
   */
  action?: string;
  _action?: Element;
  /**
   * Several agents may be associated (i.e. have some responsibility for an activity) with an event or activity.
   * For example, an activity may be initiated by one user for other users or involve more than one user. However, only one user may be the initiator/requestor for the activity.
   */
  agent: AuditEventAgent[];
  /**
   * Required unless the values for event identification, agent identification, and audit source identification are sufficient to document the entire auditable event. Because events may have more than one entity, this group can be a repeating set of values.
   */
  entity?: AuditEventEntity[];
  /**
   * In some cases a "success" may be partial, for example, an incomplete or interrupted transfer of a radiological study. For the purpose of establishing accountability, these distinctions are not relevant.
   */
  outcome?: string;
  _outcome?: Element;
  /**
   * A free text description of the outcome of the event.
   */
  outcomeDesc?: string;
  _outcomeDesc?: Element;
  /**
   * The period can be a little arbitrary; where possible, the time should correspond to human assessment of the activity time.
   */
  period?: Period;
  /**
   * Use AuditEvent.agent.purposeOfUse when you know that it is specific to the agent, otherwise use AuditEvent.purposeOfEvent. For example, during a machine-to-machine transfer it might not be obvious to the audit system who caused the event, but it does know why.
   */
  purposeOfEvent?: CodeableConcept[];
  /**
   * In a distributed system, some sort of common time base (e.g. an NTP [RFC1305] server) is a good implementation tactic.
   */
  recorded: string;
  _recorded?: Element;
  /**
   * Since multi-tier, distributed, or composite applications make source identification ambiguous, this collection of fields may repeat for each application or process actively involved in the event. For example, multiple value-sets can identify participating web servers, application processes, and database server threads in an n-tier distributed application. Passive event participants (e.g. low-level network transports) need not be identified.
   */
  source: AuditEventSource;
  /**
   * Identifier for the category of event.
   */
  subtype?: Coding[];
  /**
   * Identifier for a family of the event.  For example, a menu item, program, rule, policy, function code, application name or URL. It identifies the performed function.
   */
  type: Coding;
}
/**
 * Basic is used for handling concepts not yet defined in FHIR, narrative-only resources that don't map to an existing resource, and custom resources not appropriate for inclusion in the FHIR specification.
 */
export interface Basic extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Basic';
  /**
   * Indicates who was responsible for creating the resource instance.
   */
  author?: Reference;
  /**
   * Because resource references will only be able to indicate 'Basic', the type of reference will need to be specified in a Profile identified as part of the resource.  Refer to the resource notes section for information on appropriate terminologies for this code.
   * This element is labeled as a modifier because it defines the meaning of the resource and cannot be ignored.
   */
  code: CodeableConcept;
  /**
   * Identifies when the resource was first created.
   */
  created?: string;
  _created?: Element;
  /**
   * Identifier assigned to the resource for business purposes, outside the context of FHIR.
   */
  identifier?: Identifier[];
  /**
   * Optional as not all potential resources will have subjects.  Resources associated with multiple subjects can handle this via extension.
   */
  subject?: Reference;
}
/**
 * A resource that represents the data of a single raw artifact as digital content accessible in its native format.  A Binary resource can contain any content, whether text, image, pdf, zip archive, etc.
 */
export interface Binary extends Resource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Binary';
  /**
   * MimeType of the binary content represented as a standard MimeType (BCP 13).
   */
  contentType: string;
  _contentType?: Element;
  /**
   * If the content type is itself base64 encoding, then this will be base64 encoded twice - what is created by un-base64ing the content must be the specified content type.
   */
  data?: string;
  _data?: Element;
  /**
   * Very often, a server will also know of a resource that references the binary, and can automatically apply the appropriate access rules based on that reference. However, there are some circumstances where this is not appropriate, e.g. the binary is uploaded directly to the server without any linking resource, the binary is referred to from multiple different resources, and/or the binary is content such as an application logo that has less protection than any of the resources that reference it.
   */
  securityContext?: Reference;
}
/**
 * How this product was collected.
 */
export interface BiologicallyDerivedProductCollection extends BackboneElement {
  /**
   * Time of product collection.
   */
  collectedDateTime?: string;
  _collectedDateTime?: Element;
  /**
   * Time of product collection.
   */
  collectedPeriod?: Period;
  /**
   * Healthcare professional who is performing the collection.
   */
  collector?: Reference;
  /**
   * The patient or entity, such as a hospital or vendor in the case of a processed/manipulated/manufactured product, providing the product.
   */
  source?: Reference;
}
/**
 * Any processing of the product during collection that does not change the fundamental nature of the product. For example adding anti-coagulants during the collection of Peripheral Blood Stem Cells.
 */
export interface BiologicallyDerivedProductProcessing extends BackboneElement {
  /**
   * Substance added during processing.
   */
  additive?: Reference;
  /**
   * Description of of processing.
   */
  description?: string;
  _description?: Element;
  /**
   * Procesing code.
   */
  procedure?: CodeableConcept;
  /**
   * Time of processing.
   */
  timeDateTime?: string;
  _timeDateTime?: Element;
  /**
   * Time of processing.
   */
  timePeriod?: Period;
}
/**
 * Any manipulation of product post-collection that is intended to alter the product.  For example a buffy-coat enrichment or CD8 reduction of Peripheral Blood Stem Cells to make it more suitable for infusion.
 */
export interface BiologicallyDerivedProductManipulation extends BackboneElement {
  /**
   * Description of manipulation.
   */
  description?: string;
  _description?: Element;
  /**
   * Time of manipulation.
   */
  timeDateTime?: string;
  _timeDateTime?: Element;
  /**
   * Time of manipulation.
   */
  timePeriod?: Period;
}
/**
 * Product storage.
 */
export interface BiologicallyDerivedProductStorage extends BackboneElement {
  /**
   * Description of storage.
   */
  description?: string;
  _description?: Element;
  /**
   * Storage timeperiod.
   */
  duration?: Period;
  /**
   * Temperature scale used.
   */
  scale?: string;
  _scale?: Element;
  /**
   * Storage temperature.
   */
  temperature?: number;
}
/**
 * Code Values for the BiologicallyDerivedProduct.storage.scale field
 */
export enum BiologicallyDerivedProductStorageScaleCodes {
  FARENHEIT = 'farenheit',
  CELSIUS = 'celsius',
  KELVIN = 'kelvin',
}
/**
 * A material substance originating from a biological entity intended to be transplanted or infused
 * into another (possibly the same) biological entity.
 */
export interface BiologicallyDerivedProduct extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'BiologicallyDerivedProduct';
  /**
   * How this product was collected.
   */
  collection?: BiologicallyDerivedProductCollection;
  /**
   * This records identifiers associated with this biologically derived product instance that are defined by business processes and/or used to refer to it when a direct URL reference to the resource itself is not appropriate (e.g. in CDA documents, or in written / printed documentation).
   */
  identifier?: Identifier[];
  /**
   * Any manipulation of product post-collection that is intended to alter the product.  For example a buffy-coat enrichment or CD8 reduction of Peripheral Blood Stem Cells to make it more suitable for infusion.
   */
  manipulation?: BiologicallyDerivedProductManipulation;
  /**
   * For products that have multiple collections. For example Peripheral Blood Stem Cells may be collected over several days from a single donor and the donation split into in multiple containers which must be linked to the parent donation.
   */
  parent?: Reference[];
  /**
   * Any processing of the product during collection that does not change the fundamental nature of the product. For example adding anti-coagulants during the collection of Peripheral Blood Stem Cells.
   */
  processing?: BiologicallyDerivedProductProcessing[];
  /**
   * Broad category of this product.
   */
  productCategory?: string;
  _productCategory?: Element;
  /**
   * A code that identifies the kind of this biologically derived product (SNOMED Ctcode).
   */
  productCode?: CodeableConcept;
  /**
   * Number of discrete units within this product.
   */
  quantity?: number;
  /**
   * Procedure request to obtain this biologically derived product.
   */
  request?: Reference[];
  /**
   * Whether the product is currently available.
   */
  status?: string;
  _status?: Element;
  /**
   * Product storage.
   */
  storage?: BiologicallyDerivedProductStorage[];
}
/**
 * Code Values for the BiologicallyDerivedProduct.productCategory field
 */
export enum BiologicallyDerivedProductProductCategoryCodes {
  ORGAN = 'organ',
  TISSUE = 'tissue',
  FLUID = 'fluid',
  CELLS = 'cells',
  BIOLOGICALAGENT = 'biologicalAgent',
}
/**
 * Code Values for the BiologicallyDerivedProduct.status field
 */
export enum BiologicallyDerivedProductStatusCodes {
  AVAILABLE = 'available',
  UNAVAILABLE = 'unavailable',
}
/**
 * Record details about an anatomical structure.  This resource may be used when a coded concept does not provide the necessary detail needed for the use case.
 */
export interface BodyStructure extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'BodyStructure';
  /**
   * This element is labeled as a modifier because it may be used to mark that the resource was created in error.
   */
  active?: boolean;
  _active?: Element;
  /**
   * This description could include any visual markings used to orientate the viewer e.g. external reference points, special sutures, ink markings.
   */
  description?: string;
  _description?: Element;
  /**
   * Identifier for this instance of the anatomical structure.
   */
  identifier?: Identifier[];
  /**
   * Image or images used to identify a location.
   */
  image?: Attachment[];
  /**
   * The anatomical location or region of the specimen, lesion, or body structure.
   */
  location?: CodeableConcept;
  /**
   * Qualifier to refine the anatomical location.  These include qualifiers for laterality, relative location, directionality, number, and plane.
   */
  locationQualifier?: CodeableConcept[];
  /**
   * The minimum cardinality of 0 supports the use case of specifying a location without defining a morphology.
   */
  morphology?: CodeableConcept;
  /**
   * The person to which the body site belongs.
   */
  patient: Reference;
}
/**
 * Both Bundle.link and Bundle.entry.link are defined to support providing additional context when Bundles are used (e.g. [HATEOAS](http://en.wikipedia.org/wiki/HATEOAS)).
 * Bundle.entry.link corresponds to links found in the HTTP header if the resource in the entry was [read](http.html#read) directly.
 * This specification defines some specific uses of Bundle.link for [searching](search.html#conformance) and [paging](http.html#paging), but no specific uses for Bundle.entry.link, and no defined function in a transaction - the meaning is implementation specific.
 */
export interface BundleLink extends BackboneElement {
  /**
   * A name which details the functional use for this link - see [http://www.iana.org/assignments/link-relations/link-relations.xhtml#link-relations-1](http://www.iana.org/assignments/link-relations/link-relations.xhtml#link-relations-1).
   */
  relation: string;
  _relation?: Element;
  /**
   * The reference details for the link.
   */
  url: string;
  _url?: Element;
}
/**
 * Information about the search process that lead to the creation of this entry.
 */
export interface BundleEntrySearch extends BackboneElement {
  /**
   * There is only one mode. In some corner cases, a resource may be included because it is both a match and an include. In these circumstances, 'match' takes precedence.
   */
  mode?: string;
  _mode?: Element;
  /**
   * Servers are not required to return a ranking score. 1 is most relevant, and 0 is least relevant. Often, search results are sorted by score, but the client may specify a different sort order.
   * See [Patient Match](patient-operation-match.html) for the EMPI search which relates to this element.
   */
  score?: number;
}
/**
 * Code Values for the Bundle.entry.search.mode field
 */
export enum BundleEntrySearchModeCodes {
  MATCH = 'match',
  INCLUDE = 'include',
  OUTCOME = 'outcome',
}
/**
 * Additional information about how this entry should be processed as part of a transaction or batch.  For history, it shows how the entry was processed to create the version contained in the entry.
 */
export interface BundleEntryRequest extends BackboneElement {
  /**
   * Only perform the operation if the Etag value matches. For more information, see the API section ["Managing Resource Contention"](http.html#concurrency).
   */
  ifMatch?: string;
  _ifMatch?: Element;
  /**
   * Only perform the operation if the last updated date matches. See the API documentation for ["Conditional Read"](http.html#cread).
   */
  ifModifiedSince?: string;
  _ifModifiedSince?: Element;
  /**
   * Instruct the server not to perform the create if a specified resource already exists. For further information, see the API documentation for ["Conditional Create"](http.html#ccreate). This is just the query portion of the URL - what follows the "?" (not including the "?").
   */
  ifNoneExist?: string;
  _ifNoneExist?: Element;
  /**
   * If the ETag values match, return a 304 Not Modified status. See the API documentation for ["Conditional Read"](http.html#cread).
   */
  ifNoneMatch?: string;
  _ifNoneMatch?: Element;
  /**
   * In a transaction or batch, this is the HTTP action to be executed for this entry. In a history bundle, this indicates the HTTP action that occurred.
   */
  method: string;
  _method?: Element;
  /**
   * E.g. for a Patient Create, the method would be "POST" and the URL would be "Patient". For a Patient Update, the method would be PUT and the URL would be "Patient/[id]".
   */
  url: string;
  _url?: Element;
}
/**
 * Code Values for the Bundle.entry.request.method field
 */
export enum BundleEntryRequestMethodCodes {
  GET = 'GET',
  HEAD = 'HEAD',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}
/**
 * Indicates the results of processing the corresponding 'request' entry in the batch or transaction being responded to or what the results of an operation where when returning history.
 */
export interface BundleEntryResponse extends BackboneElement {
  /**
   * Etags match the Resource.meta.versionId. The ETag has to match the version id in the header if a resource is included.
   */
  etag?: string;
  _etag?: Element;
  /**
   * This has to match the same time in the meta header (meta.lastUpdated) if a resource is included.
   */
  lastModified?: string;
  _lastModified?: Element;
  /**
   * The location header created by processing this operation, populated if the operation returns a location.
   */
  location?: string;
  _location?: Element;
  /**
   * For a POST/PUT operation, this is the equivalent outcome that would be returned for prefer = operationoutcome - except that the resource is always returned whether or not the outcome is returned.
   * This outcome is not used for error responses in batch/transaction, only for hints and warnings. In a batch operation, the error will be in Bundle.entry.response, and for transaction, there will be a single OperationOutcome instead of a bundle in the case of an error.
   */
  outcome?: Resource;
  /**
   * The status code returned by processing this entry. The status SHALL start with a 3 digit HTTP code (e.g. 404) and may contain the standard HTTP description associated with the status code.
   */
  status: string;
  _status?: Element;
}
/**
 * An entry in a bundle resource - will either contain a resource or information about a resource (transactions and history only).
 */
export interface BundleEntry extends BackboneElement {
  /**
   * fullUrl might not be [unique in the context of a resource](bundle.html#bundle-unique). Note that since [FHIR resources do not need to be served through the FHIR API](references.html), the fullURL might be a URN or an absolute URL that does not end with the logical id of the resource (Resource.id). However, but if the fullUrl does look like a RESTful server URL (e.g. meets the [regex](references.html#regex), then the 'id' portion of the fullUrl SHALL end with the Resource.id.
   * Note that the fullUrl is not the same as the canonical URL - it's an absolute url for an endpoint serving the resource (these will happen to have the same value on the canonical server for the resource with the canonical URL).
   */
  fullUrl?: string;
  _fullUrl?: Element;
  /**
   * A series of links that provide context to this entry.
   */
  link?: BundleLink[];
  /**
   * Additional information about how this entry should be processed as part of a transaction or batch.  For history, it shows how the entry was processed to create the version contained in the entry.
   */
  request?: BundleEntryRequest;
  /**
   * The Resource for the entry. The purpose/meaning of the resource is determined by the Bundle.type.
   */
  resource?: Resource;
  /**
   * Indicates the results of processing the corresponding 'request' entry in the batch or transaction being responded to or what the results of an operation where when returning history.
   */
  response?: BundleEntryResponse;
  /**
   * Information about the search process that lead to the creation of this entry.
   */
  search?: BundleEntrySearch;
}
/**
 * A container for a collection of resources.
 */
export interface Bundle extends Resource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Bundle';
  /**
   * An entry in a bundle resource - will either contain a resource or information about a resource (transactions and history only).
   */
  entry?: BundleEntry[];
  /**
   * Persistent identity generally only matters for batches of type Document, Message, and Collection. It would not normally be populated for search and history results and servers ignore Bundle.identifier when processing batches and transactions. For Documents  the .identifier SHALL be populated such that the .identifier is globally unique.
   */
  identifier?: Identifier;
  /**
   * Both Bundle.link and Bundle.entry.link are defined to support providing additional context when Bundles are used (e.g. [HATEOAS](http://en.wikipedia.org/wiki/HATEOAS)).
   * Bundle.entry.link corresponds to links found in the HTTP header if the resource in the entry was [read](http.html#read) directly.
   * This specification defines some specific uses of Bundle.link for [searching](search.html#conformance) and [paging](http.html#paging), but no specific uses for Bundle.entry.link, and no defined function in a transaction - the meaning is implementation specific.
   */
  link?: BundleLink[];
  /**
   * The signature could be created by the "author" of the bundle or by the originating device.   Requirements around inclusion of a signature, verification of signatures and treatment of signed/non-signed bundles is implementation-environment specific.
   */
  signature?: Signature;
  /**
   * For many bundles, the timestamp is equal to .meta.lastUpdated, because they are not stored (e.g. search results). When a bundle is placed in a persistent store, .meta.lastUpdated will be usually be changed by the server. When the bundle is a message, a middleware agent altering the message (even if not stored) SHOULD update .meta.lastUpdated. .timestamp is used to track the original time of the Bundle, and SHOULD be populated.
   * Usage:
   * * document : the date the document was created. Note: the composition may predate the document, or be associated with multiple documents. The date of the composition - the authoring time - may be earlier than the document assembly time
   * * message : the date that the content of the message was assembled. This date is not changed by middleware engines unless they add additional data that changes the meaning of the time of the message
   * * history : the date that the history was assembled. This time would be used as the _since time to ask for subsequent updates
   * * searchset : the time that the search set was assembled. Note that different pages MAY have different timestamps but need not. Having different timestamps does not imply that subsequent pages will represent or include changes made since the initial query
   * * transaction | transaction-response | batch | batch-response | collection : no particular assigned meaning
   * The timestamp value should be greater than the lastUpdated and other timestamps in the resources in the bundle, and it should be equal or earlier than the .meta.lastUpdated on the Bundle itself.
   */
  timestamp?: string;
  _timestamp?: Element;
  /**
   * Only used if the bundle is a search result set. The total does not include resources such as OperationOutcome and included resources, only the total number of matching resources.
   */
  total?: number;
  /**
   * It's possible to use a bundle for other purposes (e.g. a document can be accepted as a transaction). This is primarily defined so that there can be specific rules for some of the bundle types.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the Bundle.type field
 */
export enum BundleTypeCodes {
  DOCUMENT = 'document',
  MESSAGE = 'message',
  TRANSACTION = 'transaction',
  TRANSACTION_RESPONSE = 'transaction-response',
  BATCH = 'batch',
  BATCH_RESPONSE = 'batch-response',
  HISTORY = 'history',
  SEARCHSET = 'searchset',
  COLLECTION = 'collection',
}
/**
 * Software that is covered by this capability statement.  It is used when the capability statement describes the capabilities of a particular software version, independent of an installation.
 */
export interface CapabilityStatementSoftware extends BackboneElement {
  /**
   * Name the software is known by.
   */
  name: string;
  _name?: Element;
  /**
   * Date this version of the software was released.
   */
  releaseDate?: string;
  _releaseDate?: Element;
  /**
   * If possible, a version should be specified, as statements are likely to be different for different versions of software.
   */
  version?: string;
  _version?: Element;
}
/**
 * Identifies a specific implementation instance that is described by the capability statement - i.e. a particular installation, rather than the capabilities of a software program.
 */
export interface CapabilityStatementImplementation extends BackboneElement {
  /**
   * The organization responsible for the management of the instance and oversight of the data on the server at the specified URL.
   */
  custodian?: Reference;
  /**
   * Information about the specific installation that this capability statement relates to.
   */
  description: string;
  _description?: Element;
  /**
   * An absolute base URL for the implementation.  This forms the base for REST interfaces as well as the mailbox and document interfaces.
   */
  url?: string;
  _url?: Element;
}
/**
 * Information about security implementation from an interface perspective - what a client needs to know.
 */
export interface CapabilityStatementRestSecurity extends BackboneElement {
  /**
   * The easiest CORS headers to add are Access-Control-Allow-Origin: * & Access-Control-Request-Method: GET, POST, PUT, DELETE. All servers SHOULD support CORS.
   */
  cors?: boolean;
  _cors?: Element;
  /**
   * General description of how security works.
   */
  description?: string;
  _description?: Element;
  /**
   * Types of security services that are supported/required by the system.
   */
  service?: CodeableConcept[];
}
/**
 * In general, a Resource will only appear in a CapabilityStatement if the server actually has some capabilities - e.g. there is at least one interaction supported. However interactions can be omitted to support summarization (_summary = true).
 */
export interface CapabilityStatementRestResourceInteraction extends BackboneElement {
  /**
   * Coded identifier of the operation, supported by the system resource.
   */
  code: string;
  _code?: Element;
  /**
   * Guidance specific to the implementation of this operation, such as 'delete is a logical delete' or 'updates are only allowed with version id' or 'creates permitted from pre-authorized certificates only'.
   */
  documentation?: string;
  _documentation?: Element;
}
/**
 * Code Values for the CapabilityStatement.rest.resource.interaction.code field
 */
export enum CapabilityStatementRestResourceInteractionCodeCodes {
  READ = 'read',
  VREAD = 'vread',
  UPDATE = 'update',
  PATCH = 'patch',
  DELETE = 'delete',
  HISTORY_INSTANCE = 'history-instance',
  HISTORY_TYPE = 'history-type',
  CREATE = 'create',
  SEARCH_TYPE = 'search-type',
}
/**
 * The search parameters should include the control search parameters such as _sort, _count, etc. that also apply to this resource (though many will be listed at [CapabilityStatement.rest.searchParam](capabilitystatement-definitions.html#CapabilityStatement.rest.searchParam)). The behavior of some search parameters may be further described by other code or extension elements, or narrative within the capability statement or linked [SearchParameter](searchparameter.html#) definitions.
 */
export interface CapabilityStatementRestResourceSearchParam extends BackboneElement {
  /**
   * This SHOULD be present, and matches refers to a SearchParameter by its canonical URL. If systems wish to document their support for modifiers, comparators, target resource types, and chained parameters, they should do using a search parameter resource. This element SHALL be populated if the search parameter refers to a SearchParameter defined by the FHIR core specification or externally defined IGs.
   */
  definition?: string;
  _definition?: Element;
  /**
   * This allows documentation of any distinct behaviors about how the search parameter is used.  For example, text matching algorithms.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * Parameter names cannot overlap with standard parameter names, and standard parameters cannot be redefined.
   */
  name: string;
  _name?: Element;
  /**
   * While this can be looked up from the definition, it is included here as a convenience for systems that autogenerate a query interface based on the server capability statement.  It SHALL be the same as the type in the search parameter definition.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the CapabilityStatement.rest.resource.searchParam.type field
 */
export enum CapabilityStatementRestResourceSearchParamTypeCodes {
  NUMBER = 'number',
  DATE = 'date',
  STRING = 'string',
  TOKEN = 'token',
  REFERENCE = 'reference',
  COMPOSITE = 'composite',
  QUANTITY = 'quantity',
  URI = 'uri',
  SPECIAL = 'special',
}
/**
 * Operations linked from CapabilityStatement.rest.resource.operation must have OperationDefinition.type = true or OperationDefinition.instance = true.
 * If an operation that is listed in multiple CapabilityStatement.rest.resource.operation (e.g. for different resource types), then clients should understand that the operation is only supported on the specified resource types, and that may be a subset of those listed in OperationDefinition.resource.
 */
export interface CapabilityStatementRestResourceOperation extends BackboneElement {
  /**
   * This can be used to build an HTML form to invoke the operation, for instance.
   */
  definition: string;
  _definition?: Element;
  /**
   * Documentation that describes anything special about the operation behavior, possibly detailing different behavior for system, type and instance-level invocation of the operation.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * The name here SHOULD be the same as the name in the definition, unless there is a name clash and the name cannot be used. The name does not include the "$" portion that is always included in the URL.
   */
  name: string;
  _name?: Element;
}
/**
 * Max of one repetition per resource type.
 */
export interface CapabilityStatementRestResource extends BackboneElement {
  /**
   * Conditional Create is mainly appropriate for interface engine scripts converting from other formats, such as v2.
   */
  conditionalCreate?: boolean;
  _conditionalCreate?: Element;
  /**
   * Conditional Delete is mainly appropriate for interface engine scripts converting from other formats, such as v2.
   */
  conditionalDelete?: string;
  _conditionalDelete?: Element;
  /**
   * Conditional Read is mainly appropriate for interface engine scripts converting from other formats, such as v2.
   */
  conditionalRead?: string;
  _conditionalRead?: Element;
  /**
   * Conditional Update is mainly appropriate for interface engine scripts converting from other formats, such as v2.
   */
  conditionalUpdate?: boolean;
  _conditionalUpdate?: Element;
  /**
   * Additional information about the resource type used by the system.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * In general, a Resource will only appear in a CapabilityStatement if the server actually has some capabilities - e.g. there is at least one interaction supported. However interactions can be omitted to support summarization (_summary = true).
   */
  interaction?: CapabilityStatementRestResourceInteraction[];
  /**
   * Operations linked from CapabilityStatement.rest.resource.operation must have OperationDefinition.type = true or OperationDefinition.instance = true.
   * If an operation that is listed in multiple CapabilityStatement.rest.resource.operation (e.g. for different resource types), then clients should understand that the operation is only supported on the specified resource types, and that may be a subset of those listed in OperationDefinition.resource.
   */
  operation?: CapabilityStatementRestResourceOperation[];
  /**
   * The profile applies to all  resources of this type - i.e. it is the superset of what is supported by the system.
   */
  profile?: string;
  _profile?: Element;
  /**
   * It is useful to support the vRead operation for current operations, even if past versions aren't available.
   */
  readHistory?: boolean;
  _readHistory?: Element;
  /**
   * A set of flags that defines how references are supported.
   */
  referencePolicy?: string[];
  _referencePolicy?: Element;
  /**
   * If this list is empty, the server does not support includes.
   */
  searchInclude?: string[];
  _searchInclude?: Element;
  /**
   * The search parameters should include the control search parameters such as _sort, _count, etc. that also apply to this resource (though many will be listed at [CapabilityStatement.rest.searchParam](capabilitystatement-definitions.html#CapabilityStatement.rest.searchParam)). The behavior of some search parameters may be further described by other code or extension elements, or narrative within the capability statement or linked [SearchParameter](searchparameter.html#) definitions.
   */
  searchParam?: CapabilityStatementRestResourceSearchParam[];
  /**
   * If this list is empty, the server does not support reverse includes.
   */
  searchRevInclude?: string[];
  _searchRevInclude?: Element;
  /**
   * Supported profiles are different than the profile that applies to a particular resource in .rest.resource.profile. The resource profile is a general statement of what features of the resource are supported overall by the system - the sum total of the facilities it supports. A supported profile is a deeper statement about the functionality of the data and services provided by the server (or used by the client). A typical case is a laboratory system that produces a set of different reports - this is the list of types of data that it publishes. A key aspect of declaring profiles here is the question of how the client converts knowledge that the server publishes this data into working with the data; the client can inspect individual resources to determine whether they conform to a particular profile, but how does it find the ones that do? It does so by searching using the _profile parameter, so any resources listed here must be valid values for the _profile resource (using the identifier in the target profile).
   */
  supportedProfile?: string[];
  _supportedProfile?: Element;
  /**
   * A type of resource exposed via the restful interface.
   */
  type: string;
  _type?: Element;
  /**
   * Allowing the clients to create new identities on the server means that the system administrator needs to have confidence that the clients do not create clashing identities between them. Obviously, if there is only one client, this won't happen. While creating identities on the client means that the clients need to be managed, it's much more convenient for many scenarios if such management can be put in place.
   */
  updateCreate?: boolean;
  _updateCreate?: Element;
  /**
   * If a server supports versionIds correctly, it SHOULD support vread too, but is not required to do so.
   */
  versioning?: string;
  _versioning?: Element;
}
/**
 * Code Values for the CapabilityStatement.rest.resource.conditionalDelete field
 */
export enum CapabilityStatementRestResourceConditionalDeleteCodes {
  NOT_SUPPORTED = 'not-supported',
  SINGLE = 'single',
  MULTIPLE = 'multiple',
}
/**
 * Code Values for the CapabilityStatement.rest.resource.conditionalRead field
 */
export enum CapabilityStatementRestResourceConditionalReadCodes {
  NOT_SUPPORTED = 'not-supported',
  MODIFIED_SINCE = 'modified-since',
  NOT_MATCH = 'not-match',
  FULL_SUPPORT = 'full-support',
}
/**
 * Code Values for the CapabilityStatement.rest.resource.referencePolicy field
 */
export enum CapabilityStatementRestResourceReferencePolicyCodes {
  LITERAL = 'literal',
  LOGICAL = 'logical',
  RESOLVES = 'resolves',
  ENFORCED = 'enforced',
  LOCAL = 'local',
}
/**
 * Code Values for the CapabilityStatement.rest.resource.versioning field
 */
export enum CapabilityStatementRestResourceVersioningCodes {
  NO_VERSION = 'no-version',
  VERSIONED = 'versioned',
  VERSIONED_UPDATE = 'versioned-update',
}
/**
 * A specification of restful operations supported by the system.
 */
export interface CapabilityStatementRestInteraction extends BackboneElement {
  /**
   * A coded identifier of the operation, supported by the system.
   */
  code: string;
  _code?: Element;
  /**
   * Guidance specific to the implementation of this operation, such as limitations on the kind of transactions allowed, or information about system wide search is implemented.
   */
  documentation?: string;
  _documentation?: Element;
}
/**
 * Code Values for the CapabilityStatement.rest.interaction.code field
 */
export enum CapabilityStatementRestInteractionCodeCodes {
  TRANSACTION = 'transaction',
  BATCH = 'batch',
  SEARCH_SYSTEM = 'search-system',
  HISTORY_SYSTEM = 'history-system',
}
/**
 * Multiple repetitions allow definition of both client and/or server behaviors or possibly behaviors under different configuration settings (for software or requirements statements).
 */
export interface CapabilityStatementRest extends BackboneElement {
  /**
   * At present, the only defined compartments are at [CompartmentDefinition](compartmentdefinition.html).
   */
  compartment?: string[];
  _compartment?: Element;
  /**
   * Information about the system's restful capabilities that apply across all applications, such as security.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * A specification of restful operations supported by the system.
   */
  interaction?: CapabilityStatementRestInteraction[];
  /**
   * Identifies whether this portion of the statement is describing the ability to initiate or receive restful operations.
   */
  mode: string;
  _mode?: Element;
  /**
   * CapabilityStatement.rest.operation is for operations invoked at the system level, or for operations that are supported across multiple resource types. Operations linked from CapabilityStatement.rest.operation must have OperationDefinition.system = true, or more than one Operation.resource.
   */
  operation?: CapabilityStatementRestResourceOperation[];
  /**
   * Max of one repetition per resource type.
   */
  resource?: CapabilityStatementRestResource[];
  /**
   * Typically, the only search parameters supported for all searches are those that apply to all resources - tags, profiles, text search etc. These search parameters should include the control search parameters such as _sort, _count, etc. that also apply to this resource (though many will be listed at [CapabilityStatement.rest.searchParam](capabilitystatement-definitions.html#CapabilityStatement.rest.searchParam)). The behavior of some search parameters may be further described by other code or extension elements, or narrative within the capability statement or linked [SearchParameter](searchparameter.html#) definitions.
   */
  searchParam?: CapabilityStatementRestResourceSearchParam[];
  /**
   * Information about security implementation from an interface perspective - what a client needs to know.
   */
  security?: CapabilityStatementRestSecurity;
}
/**
 * Code Values for the CapabilityStatement.rest.mode field
 */
export enum CapabilityStatementRestModeCodes {
  CLIENT = 'client',
  SERVER = 'server',
}
/**
 * An endpoint (network accessible address) to which messages and/or replies are to be sent.
 */
export interface CapabilityStatementMessagingEndpoint extends BackboneElement {
  /**
   * The network address of the endpoint. For solutions that do not use network addresses for routing, it can be just an identifier.
   */
  address: string;
  _address?: Element;
  /**
   * A list of the messaging transport protocol(s) identifiers, supported by this endpoint.
   */
  protocol: Coding;
}
/**
 * This is a proposed alternative to the messaging.event structure.
 */
export interface CapabilityStatementMessagingSupportedMessage extends BackboneElement {
  /**
   * Points to a message definition that identifies the messaging event, message structure, allowed responses, etc.
   */
  definition: string;
  _definition?: Element;
  /**
   * The mode of this event declaration - whether application is sender or receiver.
   */
  mode: string;
  _mode?: Element;
}
/**
 * Code Values for the CapabilityStatement.messaging.supportedMessage.mode field
 */
export enum CapabilityStatementMessagingSupportedMessageModeCodes {
  SENDER = 'sender',
  RECEIVER = 'receiver',
}
/**
 * Multiple repetitions allow the documentation of multiple endpoints per solution.
 */
export interface CapabilityStatementMessaging extends BackboneElement {
  /**
   * Documentation about the system's messaging capabilities for this endpoint not otherwise documented by the capability statement.  For example, the process for becoming an authorized messaging exchange partner.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * An endpoint (network accessible address) to which messages and/or replies are to be sent.
   */
  endpoint?: CapabilityStatementMessagingEndpoint[];
  /**
   * If this value is missing then the application does not implement (receiver) or depend on (sender) reliable messaging.
   */
  reliableCache?: number;
  /**
   * This is a proposed alternative to the messaging.event structure.
   */
  supportedMessage?: CapabilityStatementMessagingSupportedMessage[];
}
/**
 * A document definition.
 */
export interface CapabilityStatementDocument extends BackboneElement {
  /**
   * A description of how the application supports or uses the specified document profile.  For example, when documents are created, what action is taken with consumed documents, etc.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * Mode of this document declaration - whether an application is a producer or consumer.
   */
  mode: string;
  _mode?: Element;
  /**
   * The profile is actually on the Bundle.
   */
  profile: string;
  _profile?: Element;
}
/**
 * Code Values for the CapabilityStatement.document.mode field
 */
export enum CapabilityStatementDocumentModeCodes {
  PRODUCER = 'producer',
  CONSUMER = 'consumer',
}
/**
 * A Capability Statement documents a set of capabilities (behaviors) of a FHIR Server for a particular version of FHIR that may be used as a statement of actual server functionality or a statement of required or desired server implementation.
 */
export interface CapabilityStatement extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'CapabilityStatement';
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the capability statement and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the capability statement.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the capability statement. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the capability statement was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the capability statement as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the capability statement is presumed to be the predominant language in the place the capability statement was created).This does not need to be populated if the description is adequately implied by the software or implementation details.
   */
  description?: string;
  _description?: Element;
  /**
   * A document definition.
   */
  document?: CapabilityStatementDocument[];
  /**
   * Allows filtering of capability statements that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Servers may implement multiple versions (see [Managing Multiple Versions](versioning.html), and the [$versions](capabilitystatement-operation-versions.html) operation). If they do, and the CapabilityStatement is requested from the server, then this fhirVersion will be either the version requested, or the server's default version.
   */
  fhirVersion: string;
  _fhirVersion?: Element;
  /**
   * "xml", "json" and "ttl" are allowed, which describe the simple encodings described in the specification (and imply appropriate bundle support). Otherwise, mime types are legal here.
   */
  format: string[];
  _format?: Element;
  /**
   * Identifies a specific implementation instance that is described by the capability statement - i.e. a particular installation, rather than the capabilities of a software program.
   */
  implementation?: CapabilityStatementImplementation;
  /**
   * A list of implementation guides that the server does (or should) support in their entirety.
   */
  implementationGuide?: string[];
  _implementationGuide?: Element;
  /**
   * the contents of any directly or indirectly imported CapabilityStatements SHALL NOT overlap, i.e. they cannot refer to the same rest/resource, operations/name, searchparam/name, interaction/code, messaging/endpoint, document/mode pair.
   * A capability statement that imports another CapabilityStatement automatically instantiates it too (though this is often not a very useful statement for the kinds of CapabilityStatements that are suitable for importing).
   */
  imports?: string[];
  _imports?: Element;
  /**
   * HL7 defines the following Services: [Terminology Service](terminology-service.html).
   * Many [Implementation Guides](http://fhir.org/guides/registry) define additional services.
   */
  instantiates?: string[];
  _instantiates?: Element;
  /**
   * It may be possible for the capability statement to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * The way that this statement is intended to be used, to describe an actual running instance of software, a particular product (kind, not instance of software) or a class of implementation (e.g. a desired purchase).
   */
  kind: string;
  _kind?: Element;
  /**
   * Multiple repetitions allow the documentation of multiple endpoints per solution.
   */
  messaging?: CapabilityStatementMessaging[];
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * At present, the patch mime types application/json-patch+json and application/xml-patch+xml are legal. Generally, if a server supports PATCH, it would be expected to support the patch formats and match the formats it supports, but this is not always possible or necessary.
   */
  patchFormat?: string[];
  _patchFormat?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the capability statement is the organization or individual primarily responsible for the maintenance and upkeep of the capability statement. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the capability statement. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the capability statement. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this capability statement.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Multiple repetitions allow definition of both client and/or server behaviors or possibly behaviors under different configuration settings (for software or requirements statements).
   */
  rest?: CapabilityStatementRest[];
  /**
   * Software that is covered by this capability statement.  It is used when the capability statement describes the capabilities of a particular software version, independent of an installation.
   */
  software?: CapabilityStatementSoftware;
  /**
   * Allows filtering of capability statements that are appropriate for use versus not.This is not intended for use with actual capability statements, but where capability statements are used to describe possible or desired systems.
   */
  status: string;
  _status?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different capability statement instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the capability statement with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the CapabilityStatement.format field
 */
export enum CapabilityStatementFormatCodes {
  FORMATS = 'formats',
  JSON = 'json',
  TTL = 'ttl',
  MIME = 'mime',
}
/**
 * Code Values for the CapabilityStatement.kind field
 */
export enum CapabilityStatementKindCodes {
  INSTANCE = 'instance',
  CAPABILITY = 'capability',
  REQUIREMENTS = 'requirements',
}
/**
 * Code Values for the CapabilityStatement.status field
 */
export enum CapabilityStatementStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * A simple summary of a planned activity suitable for a general care plan system (e.g. form driven) that doesn't know about specific resources such as procedure etc.
 */
export interface CarePlanActivityDetail extends BackboneElement {
  /**
   * Tends to be less relevant for activities involving particular products.  Codes should not convey negation - use "prohibited" instead.
   */
  code?: CodeableConcept;
  /**
   * Identifies the quantity expected to be consumed in a given day.
   */
  dailyAmount?: Quantity;
  /**
   * This provides a textual description of constraints on the intended activity occurrence, including relation to other activities.  It may also include objectives, pre-conditions and end-conditions.  Finally, it may convey specifics about the activity such as body site, method, route, etc.
   */
  description?: string;
  _description?: Element;
  /**
   * This element is labeled as a modifier because it marks an activity as an activity that is not to be performed.
   */
  doNotPerform?: boolean;
  _doNotPerform?: Element;
  /**
   * Internal reference that identifies the goals that this activity is intended to contribute towards meeting.
   */
  goal?: Reference[];
  /**
   * The URL pointing to a FHIR-defined protocol, guideline, questionnaire or other definition that is adhered to in whole or in part by this CarePlan activity.
   */
  instantiatesCanonical?: string[];
  _instantiatesCanonical?: Element;
  /**
   * This might be an HTML page, PDF, etc. or could just be a non-resolvable URI identifier.
   */
  instantiatesUri?: string[];
  _instantiatesUri?: Element;
  /**
   * A description of the kind of resource the in-line definition of a care plan activity is representing.  The CarePlan.activity.detail is an in-line definition when a resource is not referenced using CarePlan.activity.reference.  For example, a MedicationRequest, a ServiceRequest, or a CommunicationRequest.
   */
  kind?: string;
  _kind?: Element;
  /**
   * May reference a specific clinical location or may identify a type of location.
   */
  location?: Reference;
  /**
   * A performer MAY also be a participant in the care plan.
   */
  performer?: Reference[];
  /**
   * Identifies the food, drug or other product to be consumed or supplied in the activity.
   */
  productCodeableConcept?: CodeableConcept;
  /**
   * Identifies the food, drug or other product to be consumed or supplied in the activity.
   */
  productReference?: Reference;
  /**
   * Identifies the quantity expected to be supplied, administered or consumed by the subject.
   */
  quantity?: Quantity;
  /**
   * This could be a diagnosis code.  If a full condition record exists or additional detail is needed, use reasonCondition instead.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Conditions can be identified at the activity level that are not identified as reasons for the overall plan.
   */
  reasonReference?: Reference[];
  /**
   * The period, timing or frequency upon which the described activity is to occur.
   */
  scheduledTiming?: Timing;
  /**
   * The period, timing or frequency upon which the described activity is to occur.
   */
  scheduledPeriod?: Period;
  /**
   * The period, timing or frequency upon which the described activity is to occur.
   */
  scheduledString?: string;
  _scheduledString?: Element;
  /**
   * Some aspects of status can be inferred based on the resources linked in actionTaken.  Note that "status" is only as current as the plan was most recently updated.
   * The unknown code is not to be used to convey other statuses.  The unknown code should be used when one of the statuses applies, but the authoring system doesn't know the current state of the activity.
   */
  status: string;
  _status?: Element;
  /**
   * Will generally not be present if status is "complete".  Be sure to prompt to update this (or at least remove the existing value) if the status is changed.
   */
  statusReason?: CodeableConcept;
}
/**
 * Code Values for the CarePlan.activity.detail.kind field
 */
export enum CarePlanActivityDetailKindCodes {
  APPOINTMENT = 'Appointment',
  COMMUNICATIONREQUEST = 'CommunicationRequest',
  DEVICEREQUEST = 'DeviceRequest',
  MEDICATIONREQUEST = 'MedicationRequest',
  NUTRITIONORDER = 'NutritionOrder',
  TASK = 'Task',
  SERVICEREQUEST = 'ServiceRequest',
  VISIONPRESCRIPTION = 'VisionPrescription',
}
/**
 * Code Values for the CarePlan.activity.detail.status field
 */
export enum CarePlanActivityDetailStatusCodes {
  NOT_STARTED = 'not-started',
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in-progress',
  ON_HOLD = 'on-hold',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  STOPPED = 'stopped',
  UNKNOWN = 'unknown',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Identifies a planned action to occur as part of the plan.  For example, a medication to be used, lab tests to perform, self-monitoring, education, etc.
 */
export interface CarePlanActivity extends BackboneElement {
  /**
   * A simple summary of a planned activity suitable for a general care plan system (e.g. form driven) that doesn't know about specific resources such as procedure etc.
   */
  detail?: CarePlanActivityDetail;
  /**
   * Note that this should not duplicate the activity status (e.g. completed or in progress).
   */
  outcomeCodeableConcept?: CodeableConcept[];
  /**
   * The activity outcome is independent of the outcome of the related goal(s).  For example, if the goal is to achieve a target body weight of 150 lbs and an activity is defined to diet, then the activity outcome could be calories consumed whereas the goal outcome is an observation for the actual body weight measured.
   */
  outcomeReference?: Reference[];
  /**
   * This element should NOT be used to describe the activity to be performed - that occurs either within the resource pointed to by activity.detail.reference or in activity.detail.description.
   */
  progress?: Annotation[];
  /**
   * Standard extension exists ([resource-pertainsToGoal](extension-resource-pertainstogoal.html)) that allows goals to be referenced from any of the referenced resources in CarePlan.activity.reference.
   * The goal should be visible when the resource referenced by CarePlan.activity.reference is viewed independently from the CarePlan.  Requests that are pointed to by a CarePlan using this element should *not* point to this CarePlan using the "basedOn" element.  i.e. Requests that are part of a CarePlan are not "based on" the CarePlan.
   */
  reference?: Reference;
}
/**
 * Describes the intention of how one or more practitioners intend to deliver care for a particular patient, group or community for a period of time, possibly limited to care for a specific condition or set of conditions.
 */
export interface CarePlan extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'CarePlan';
  /**
   * Identifies a planned action to occur as part of the plan.  For example, a medication to be used, lab tests to perform, self-monitoring, education, etc.
   */
  activity?: CarePlanActivity[];
  /**
   * When the diagnosis is related to an allergy or intolerance, the Condition and AllergyIntolerance resources can both be used. However, to be actionable for decision support, using Condition alone is not sufficient as the allergy or intolerance condition needs to be represented as an AllergyIntolerance.
   */
  addresses?: Reference[];
  /**
   * The author may also be a contributor.  For example, an organization can be an author, but not listed as a contributor.
   */
  author?: Reference;
  /**
   * A care plan that is fulfilled in whole or in part by this care plan.
   */
  basedOn?: Reference[];
  /**
   * Identifies all people and organizations who are expected to be involved in the care envisioned by this plan.
   */
  careTeam?: Reference[];
  /**
   * There may be multiple axes of categorization and one plan may serve multiple purposes.  In some cases, this may be redundant with references to CarePlan.concern.
   */
  category?: CodeableConcept[];
  /**
   * Collaborative care plans may have multiple contributors.
   */
  contributor?: Reference[];
  /**
   * Represents when this particular CarePlan record was created in the system, which is often a system-generated date.
   */
  created?: string;
  _created?: Element;
  /**
   * A description of the scope and nature of the plan.
   */
  description?: string;
  _description?: Element;
  /**
   * This will typically be the encounter the event occurred within, but some activities may be initiated prior to or after the official completion of an encounter but still be tied to the context of the encounter. CarePlan activities conducted as a result of the care plan may well occur as part of other encounters.
   */
  encounter?: Reference;
  /**
   * Goal can be achieving a particular change or merely maintaining a current state or even slowing a decline.
   */
  goal?: Reference[];
  /**
   * This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  identifier?: Identifier[];
  /**
   * The URL pointing to a FHIR-defined protocol, guideline, questionnaire or other definition that is adhered to in whole or in part by this CarePlan.
   */
  instantiatesCanonical?: string[];
  _instantiatesCanonical?: Element;
  /**
   * This might be an HTML page, PDF, etc. or could just be a non-resolvable URI identifier.
   */
  instantiatesUri?: string[];
  _instantiatesUri?: Element;
  /**
   * This element is labeled as a modifier because the intent alters when and how the resource is actually applicable.
   */
  intent: string;
  _intent?: Element;
  /**
   * General notes about the care plan not covered elsewhere.
   */
  note?: Annotation[];
  /**
   * Each care plan is an independent request, such that having a care plan be part of another care plan can cause issues with cascading statuses.  As such, this element is still being discussed.
   */
  partOf?: Reference[];
  /**
   * Any activities scheduled as part of the plan should be constrained to the specified period regardless of whether the activities are planned within a single encounter/episode or across multiple encounters/episodes (e.g. the longitudinal management of a chronic condition).
   */
  period?: Period;
  /**
   * The replacement could be because the initial care plan was immediately rejected (due to an issue) or because the previous care plan was completed, but the need for the action described by the care plan remains ongoing.
   */
  replaces?: Reference[];
  /**
   * The unknown code is not to be used to convey other statuses.  The unknown code should be used when one of the statuses applies, but the authoring system doesn't know the current state of the care plan.
   * This element is labeled as a modifier because the status contains the code entered-in-error that marks the plan as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * Identifies the patient or group whose intended care is described by the plan.
   */
  subject: Reference;
  /**
   * Use "concern" to identify specific conditions addressed by the care plan.
   */
  supportingInfo?: Reference[];
  /**
   * Human-friendly name for the care plan.
   */
  title?: string;
  _title?: Element;
}
/**
 * Code Values for the CarePlan.intent field
 */
export enum CarePlanIntentCodes {
  PROPOSAL = 'proposal',
  PLAN = 'plan',
  ORDER = 'order',
  OPTION = 'option',
}
/**
 * Code Values for the CarePlan.status field
 */
export enum CarePlanStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  ON_HOLD = 'on-hold',
  REVOKED = 'revoked',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  UNKNOWN = 'unknown',
}
/**
 * Identifies all people and organizations who are expected to be involved in the care team.
 */
export interface CareTeamParticipant extends BackboneElement {
  /**
   * Patient only needs to be listed if they have a role other than "subject of care".
   * Member is optional because some participants may be known only by their role, particularly in draft plans.
   */
  member?: Reference;
  /**
   * The organization of the practitioner.
   */
  onBehalfOf?: Reference;
  /**
   * Indicates when the specific member or organization did (or is intended to) come into effect and end.
   */
  period?: Period;
  /**
   * Roles may sometimes be inferred by type of Practitioner.  These are relationships that hold only within the context of the care team.  General relationships should be handled as properties of the Patient resource directly.
   */
  role?: CodeableConcept[];
}
/**
 * The Care Team includes all the people and organizations who plan to participate in the coordination and delivery of care for a patient.
 */
export interface CareTeam extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'CareTeam';
  /**
   * There may be multiple axis of categorization and one team may serve multiple purposes.
   */
  category?: CodeableConcept[];
  /**
   * This will typically be the encounter the event occurred within, but some activities may be initiated prior to or after the official completion of an encounter but still be tied to the context of the encounter.
   */
  encounter?: Reference;
  /**
   * This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  identifier?: Identifier[];
  /**
   * The organization responsible for the care team.
   */
  managingOrganization?: Reference[];
  /**
   * The meaning/purpose of the team is conveyed in CareTeam.category.  This element may also convey semantics of the team (e.g. "Red trauma team"), but its primary purpose is to distinguish between identical teams in a human-friendly way.  ("Team 18735" isn't as friendly.).
   */
  name?: string;
  _name?: Element;
  /**
   * Comments made about the CareTeam.
   */
  note?: Annotation[];
  /**
   * Identifies all people and organizations who are expected to be involved in the care team.
   */
  participant?: CareTeamParticipant[];
  /**
   * Indicates when the team did (or is intended to) come into effect and end.
   */
  period?: Period;
  /**
   * Describes why the care team exists.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Condition(s) that this care team addresses.
   */
  reasonReference?: Reference[];
  /**
   * This element is labeled as a modifier because the status contains the code entered-in-error that marks the care team as not currently valid.
   */
  status?: string;
  _status?: Element;
  /**
   * Identifies the patient or group whose intended care is handled by the team.
   */
  subject?: Reference;
  /**
   * The ContactPoint.use code of home is not appropriate to use. These contacts are not the contact details of individual care team members.
   */
  telecom?: ContactPoint[];
}
/**
 * Code Values for the CareTeam.status field
 */
export enum CareTeamStatusCodes {
  PROPOSED = 'proposed',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  INACTIVE = 'inactive',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Used for example, to point to a substance, or to a device used to administer a medication.
 */
export interface CatalogEntryRelatedEntry extends BackboneElement {
  /**
   * The reference to the related item.
   */
  item: Reference;
  /**
   * The type of relation to the related item: child, parent, packageContent, containerPackage, usedIn, uses, requires, etc.
   */
  relationtype: string;
  _relationtype?: Element;
}
/**
 * Code Values for the CatalogEntry.relatedEntry.relationtype field
 */
export enum CatalogEntryRelatedEntryRelationtypeCodes {
  TRIGGERS = 'triggers',
  IS_REPLACED_BY = 'is-replaced-by',
}
/**
 * Catalog entries are wrappers that contextualize items included in a catalog.
 */
export interface CatalogEntry extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'CatalogEntry';
  /**
   * Used for examplefor Out of Formulary, or any specifics.
   */
  additionalCharacteristic?: CodeableConcept[];
  /**
   * User for example for ATC classification, or.
   */
  additionalClassification?: CodeableConcept[];
  /**
   * Used in supporting related concepts, e.g. NDC to RxNorm.
   */
  additionalIdentifier?: Identifier[];
  /**
   * Classes of devices, or ATC for medication.
   */
  classification?: CodeableConcept[];
  /**
   * Used in supporting different identifiers for the same product, e.g. manufacturer code and retailer code.
   */
  identifier?: Identifier[];
  /**
   * Perhaps not needed - if we use fhir resource metadata.
   */
  lastUpdated?: string;
  _lastUpdated?: Element;
  /**
   * Whether the entry represents an orderable item.
   */
  orderable: boolean;
  _orderable?: Element;
  /**
   * The item in a catalog or definition.
   */
  referencedItem: Reference;
  /**
   * Used for example, to point to a substance, or to a device used to administer a medication.
   */
  relatedEntry?: CatalogEntryRelatedEntry[];
  /**
   * Used to support catalog exchange even for unsupported products, e.g. getting list of medications even if not prescribable.
   */
  status?: string;
  _status?: Element;
  /**
   * The type of item - medication, device, service, protocol or other.
   */
  type?: CodeableConcept;
  /**
   * The time period in which this catalog entry is expected to be active.
   */
  validityPeriod?: Period;
  /**
   * The date until which this catalog entry is expected to be active.
   */
  validTo?: string;
  _validTo?: Element;
}
/**
 * Code Values for the CatalogEntry.status field
 */
export enum CatalogEntryStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Indicates who or what performed or participated in the charged service.
 */
export interface ChargeItemPerformer extends BackboneElement {
  /**
   * The device, practitioner, etc. who performed or participated in the service.
   */
  actor: Reference;
  /**
   * Describes the type of performance or participation(e.g. primary surgeon, anesthesiologiest, etc.).
   */
  function?: CodeableConcept;
}
/**
 * The resource ChargeItem describes the provision of healthcare provider products for a certain patient, therefore referring not only to the product, but containing in addition details of the provision, like date, time, amounts and participating organizations and persons. Main Usage of the ChargeItem is to enable the billing process and internal cost allocation.
 */
export interface ChargeItem extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ChargeItem';
  /**
   * Systems posting the ChargeItems might not always be able to determine, which accounts the Items need to be places into. It is up to the postprocessing Financial System to apply internal rules to decide based on the Encounter/EpisodeOfCare/Patient/Coverage context and the type of ChargeItem, which Account is appropriate.
   */
  account?: Reference[];
  /**
   * Only used if not implicit in code found in Condition.code. If the use case requires attributes from the BodySite resource (e.g. to identify and track separately) then use the standard extension [bodySite](extension-bodysite.html).  May be a summary code, or a reference to a very precise definition of the location, or both.
   */
  bodysite?: CodeableConcept[];
  /**
   * A code that identifies the charge, like a billing code.
   */
  code: CodeableConcept;
  /**
   * The encounter or episode of care that establishes the context for this event.
   */
  context?: Reference;
  /**
   * The costCenter could either be given as a reference to an Organization(Role) resource or as the identifier of the cost center determined by Reference.identifier.value and Reference.identifier.system, depending on use case requirements.
   */
  costCenter?: Reference;
  /**
   * References the source of pricing information, rules of application for the code this ChargeItem uses.
   */
  definitionCanonical?: string[];
  _definitionCanonical?: Element;
  /**
   * References the (external) source of pricing information, rules of application for the code this ChargeItem uses.
   */
  definitionUri?: string[];
  _definitionUri?: Element;
  /**
   * The actual date when the service associated with the charge has been rendered is captured in occurrence[x].
   */
  enteredDate?: string;
  _enteredDate?: Element;
  /**
   * The enterer is also the person considered responsible for factor/price overrides if applicable.
   */
  enterer?: Reference;
  /**
   * There is no reason to carry the factor in the instance of a ChargeItem unless special circumstances require a manual override. The factors are usually defined by a set of rules in a back catalogue of the billing codes  (see ChargeItem.definition). Derived profiles may require a ChargeItem.overrideReason to be provided if either factor or price are manually overridden.
   */
  factorOverride?: number;
  /**
   * Identifiers assigned to this event performer or other systems.
   */
  identifier?: Identifier[];
  /**
   * Comments made about the event by the performer, subject or other participants.
   */
  note?: Annotation[];
  /**
   * The list of types may be constrained as appropriate for the type of charge item.
   */
  occurrenceDateTime?: string;
  _occurrenceDateTime?: Element;
  /**
   * The list of types may be constrained as appropriate for the type of charge item.
   */
  occurrencePeriod?: Period;
  /**
   * The list of types may be constrained as appropriate for the type of charge item.
   */
  occurrenceTiming?: Timing;
  /**
   * Derived Profiles may choose to add invariants requiring this field to be populated if either priceOverride or factorOverride have been filled.
   */
  overrideReason?: string;
  _overrideReason?: Element;
  /**
   * ChargeItems can be grouped to larger ChargeItems covering the whole set.
   */
  partOf?: Reference[];
  /**
   * Indicates who or what performed or participated in the charged service.
   */
  performer?: ChargeItemPerformer[];
  /**
   * Practitioners and Devices can be associated with multiple organizations. It has to be made clear, on behalf of which Organization the services have been rendered.
   */
  performingOrganization?: Reference;
  /**
   * There is no reason to carry the price in the instance of a ChargeItem unless circumstances require a manual override. The list prices or are usually defined in a back catalogue of the billing codes  (see ChargeItem.definition). Derived profiles may require a ChargeItem.overrideReason to be provided if either factor or price are manually overridden.
   */
  priceOverride?: Money;
  /**
   * Identifies the device, food, drug or other product being charged either by type code or reference to an instance.
   */
  productReference?: Reference;
  /**
   * Identifies the device, food, drug or other product being charged either by type code or reference to an instance.
   */
  productCodeableConcept?: CodeableConcept;
  /**
   * In many cases this may just be a value, if the underlying units are implicit in the definition of the charge item code.
   */
  quantity?: Quantity;
  /**
   * If the application of the charge item requires a reason to be given, it can be captured here. Textual reasons can be captured using reasonCode.text.
   */
  reason?: CodeableConcept[];
  /**
   * The rendered Service might not be associated with a Request. This property indicates which Organization requested the services to be rendered. (In many cases, this may just be the Department associated with the Encounter.location).
   */
  requestingOrganization?: Reference;
  /**
   * Indicated the rendered service that caused this charge.
   */
  service?: Reference[];
  /**
   * Unknown does not represent "other" - one of the defined statuses must apply.  Unknown is used when the authoring system is not sure what the current status is.
   * This element is labeled as a modifier because the status contains the code entered-in-error that marks the charge item as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * The individual or set of individuals the action is being or was performed on.
   */
  subject: Reference;
  /**
   * Further information supporting this charge.
   */
  supportingInformation?: Reference[];
}
/**
 * Code Values for the ChargeItem.status field
 */
export enum ChargeItemStatusCodes {
  PLANNED = 'planned',
  BILLABLE = 'billable',
  NOT_BILLABLE = 'not-billable',
  ABORTED = 'aborted',
  BILLED = 'billed',
  ENTERED_IN_ERROR = 'entered-in-error',
  UNKNOWN = 'unknown',
}
/**
 * The applicability conditions can be used to ascertain whether a billing item is allowed in a specific context. E.g. some billing codes may only be applicable in out-patient settings, only to male/female patients or only to children.
 */
export interface ChargeItemDefinitionApplicability extends BackboneElement {
  /**
   * A brief, natural language description of the condition that effectively communicates the intended semantics.
   */
  description?: string;
  _description?: Element;
  /**
   * Please note that FHIRPath Expressions can only be evaluated in the scope of the current ChargeItem resource to which this definition is being applied.
   * FHIRPath expressions can traverse into other resources linked from the ChargeItem resource, however, testing rules such as that a billing code may be billed only once per encounter need a wider scope. In such scenarios, CQL may be the appropriate choice.
   */
  expression?: string;
  _expression?: Element;
  /**
   * The media type of the language for the expression, e.g. "text/cql" for Clinical Query Language expressions or "text/fhirpath" for FHIRPath expressions.
   */
  language?: string;
  _language?: Element;
}
/**
 * The price for a ChargeItem may be calculated as a base price with surcharges/deductions that apply in certain conditions. A ChargeItemDefinition resource that defines the prices, factors and conditions that apply to a billing code is currently under development. The priceComponent element can be used to offer transparency to the recipient of the Invoice of how the prices have been calculated.
 */
export interface ChargeItemDefinitionPropertyGroupPriceComponent extends BackboneElement {
  /**
   * The amount calculated for this component.
   */
  amount?: Money;
  /**
   * A code that identifies the component. Codes may be used to differentiate between kinds of taxes, surcharges, discounts etc.
   */
  code?: CodeableConcept;
  /**
   * The factor that has been applied on the base price for calculating this component.
   */
  factor?: number;
  /**
   * This code identifies the type of the component.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the ChargeItemDefinition.propertyGroup.priceComponent.type field
 */
export enum ChargeItemDefinitionPropertyGroupPriceComponentTypeCodes {
  BASE = 'base',
  SURCHARGE = 'surcharge',
  DEDUCTION = 'deduction',
  DISCOUNT = 'discount',
  TAX = 'tax',
  INFORMATIONAL = 'informational',
}
/**
 * Group of properties which are applicable under the same conditions. If no applicability rules are established for the group, then all properties always apply.
 */
export interface ChargeItemDefinitionPropertyGroup extends BackboneElement {
  /**
   * The applicability conditions can be used to ascertain whether a billing item is allowed in a specific context. E.g. some billing codes may only be applicable in out-patient settings, only to male/female patients or only to children.
   */
  applicability?: ChargeItemDefinitionApplicability[];
  /**
   * The price for a ChargeItem may be calculated as a base price with surcharges/deductions that apply in certain conditions. A ChargeItemDefinition resource that defines the prices, factors and conditions that apply to a billing code is currently under development. The priceComponent element can be used to offer transparency to the recipient of the Invoice of how the prices have been calculated.
   */
  priceComponent?: ChargeItemDefinitionPropertyGroupPriceComponent[];
}
/**
 * The ChargeItemDefinition resource provides the properties that apply to the (billing) codes necessary to calculate costs and prices. The properties may differ largely depending on type and realm, therefore this resource gives only a rough structure and requires profiling for each type of billing code system.
 */
export interface ChargeItemDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ChargeItemDefinition';
  /**
   * The applicability conditions can be used to ascertain whether a billing item is allowed in a specific context. E.g. some billing codes may only be applicable in out-patient settings, only to male/female patients or only to children.
   */
  applicability?: ChargeItemDefinitionApplicability[];
  /**
   * The 'date' element may be more recent than the approval date because of minor changes or editorial corrections.
   */
  approvalDate?: string;
  _approvalDate?: Element;
  /**
   * The defined billing details in this resource pertain to the given billing code.
   */
  code?: CodeableConcept;
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the charge item definition and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the charge item definition.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the charge item definition. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * The URL pointing to an externally-defined charge item definition that is adhered to in whole or in part by this definition.
   */
  derivedFromUri?: string[];
  _derivedFromUri?: Element;
  /**
   * This description can be used to capture details such as why the charge item definition was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the charge item definition as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the charge item definition is presumed to be the predominant language in the place the charge item definition was created).
   */
  description?: string;
  _description?: Element;
  /**
   * The effective period for a charge item definition  determines when the content is applicable for usage and is independent of publication and review dates. For example, a measure intended to be used for the year 2016 might be published in 2015.
   */
  effectivePeriod?: Period;
  /**
   * Allows filtering of charge item definitions that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this charge item definition outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * In case of highly customized, individually produced or fitted devices/substances, the pricing information may be different for each instance of the product. This reference links pricing details to specific product instances.
   */
  instance?: Reference[];
  /**
   * It may be possible for the charge item definition to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * If specified, this date follows the original approval date.
   */
  lastReviewDate?: string;
  _lastReviewDate?: Element;
  /**
   * A larger definition of which this particular definition is a component or step.
   */
  partOf?: string[];
  _partOf?: Element;
  /**
   * Group of properties which are applicable under the same conditions. If no applicability rules are established for the group, then all properties always apply.
   */
  propertyGroup?: ChargeItemDefinitionPropertyGroup[];
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the charge item definition is the organization or individual primarily responsible for the maintenance and upkeep of the charge item definition. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the charge item definition. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * As new versions of a protocol or guideline are defined, allows identification of what versions are replaced by a new instance.
   */
  replaces?: string[];
  _replaces?: Element;
  /**
   * Allows filtering of charge item definitions that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different charge item definition instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the charge item definition with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the ChargeItemDefinition.status field
 */
export enum ChargeItemDefinitionStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * For example,  for the original treatment and follow-up exams.
 */
export interface ClaimRelated extends BackboneElement {
  /**
   * Reference to a related claim.
   */
  claim?: Reference;
  /**
   * For example, Property/Casualty insurer claim # or Workers Compensation case # .
   */
  reference?: Identifier;
  /**
   * For example, prior claim or umbrella.
   */
  relationship?: CodeableConcept;
}
/**
 * Often providers agree to receive the benefits payable to reduce the near-term costs to the patient. The insurer may decline to pay the provider and choose to pay the subscriber instead.
 */
export interface ClaimPayee extends BackboneElement {
  /**
   * Not required if the payee is 'subscriber' or 'provider'.
   */
  party?: Reference;
  /**
   * Type of Party to be reimbursed: subscriber, provider, other.
   */
  type: CodeableConcept;
}
/**
 * The members of the team who provided the products and services.
 */
export interface ClaimCareTeam extends BackboneElement {
  /**
   * Member of the team who provided the product or service.
   */
  provider: Reference;
  /**
   * The qualification of the practitioner which is applicable for this service.
   */
  qualification?: CodeableConcept;
  /**
   * Responsible might not be required when there is only a single provider listed.
   */
  responsible?: boolean;
  _responsible?: Element;
  /**
   * Role might not be required when there is only a single provider listed.
   */
  role?: CodeableConcept;
  /**
   * A number to uniquely identify care team entries.
   */
  sequence: number;
}
/**
 * Often there are multiple jurisdiction specific valuesets which are required.
 */
export interface ClaimSupportingInfo extends BackboneElement {
  /**
   * This may contain a category for the local bill type codes.
   */
  category: CodeableConcept;
  /**
   * System and code pertaining to the specific information regarding special conditions relating to the setting, treatment or patient  for which care is sought.
   */
  code?: CodeableConcept;
  /**
   * For example: the reason for the additional stay, or why a tooth is  missing.
   */
  reason?: CodeableConcept;
  /**
   * A number to uniquely identify supporting information entries.
   */
  sequence: number;
  /**
   * The date when or period to which this information refers.
   */
  timingDate?: string;
  _timingDate?: Element;
  /**
   * The date when or period to which this information refers.
   */
  timingPeriod?: Period;
  /**
   * Could be used to provide references to other resources, document. For example could contain a PDF in an Attachment of the Police Report for an Accident.
   */
  valueBoolean?: boolean;
  _valueBoolean?: Element;
  /**
   * Could be used to provide references to other resources, document. For example could contain a PDF in an Attachment of the Police Report for an Accident.
   */
  valueString?: string;
  _valueString?: Element;
  /**
   * Could be used to provide references to other resources, document. For example could contain a PDF in an Attachment of the Police Report for an Accident.
   */
  valueQuantity?: Quantity;
  /**
   * Could be used to provide references to other resources, document. For example could contain a PDF in an Attachment of the Police Report for an Accident.
   */
  valueAttachment?: Attachment;
  /**
   * Could be used to provide references to other resources, document. For example could contain a PDF in an Attachment of the Police Report for an Accident.
   */
  valueReference?: Reference;
}
/**
 * Information about diagnoses relevant to the claim items.
 */
export interface ClaimDiagnosis extends BackboneElement {
  /**
   * The nature of illness or problem in a coded form or as a reference to an external defined Condition.
   */
  diagnosisCodeableConcept: CodeableConcept;
  /**
   * The nature of illness or problem in a coded form or as a reference to an external defined Condition.
   */
  diagnosisReference: Reference;
  /**
   * Indication of whether the diagnosis was present on admission to a facility.
   */
  onAdmission?: CodeableConcept;
  /**
   * For example DRG (Diagnosis Related Group) or a bundled billing code. A patient may have a diagnosis of a Myocardial Infarction and a DRG for HeartAttack would be assigned. The Claim item (and possible subsequent claims) would refer to the DRG for those line items that were for services related to the heart attack event.
   */
  packageCode?: CodeableConcept;
  /**
   * Diagnosis are presented in list order to their expected importance: primary, secondary, etc.
   */
  sequence: number;
  /**
   * For example: admitting, primary, secondary, discharge.
   */
  type?: CodeableConcept[];
}
/**
 * Procedures performed on the patient relevant to the billing items with the claim.
 */
export interface ClaimProcedure extends BackboneElement {
  /**
   * Date and optionally time the procedure was performed.
   */
  date?: string;
  _date?: Element;
  /**
   * The code or reference to a Procedure resource which identifies the clinical intervention performed.
   */
  procedureCodeableConcept: CodeableConcept;
  /**
   * The code or reference to a Procedure resource which identifies the clinical intervention performed.
   */
  procedureReference: Reference;
  /**
   * A number to uniquely identify procedure entries.
   */
  sequence: number;
  /**
   * For example: primary, secondary.
   */
  type?: CodeableConcept[];
  /**
   * Unique Device Identifiers associated with this line item.
   */
  udi?: Reference[];
}
/**
 * All insurance coverages for the patient which may be applicable for reimbursement, of the products and services listed in the claim, are typically provided in the claim to allow insurers to confirm the ordering of the insurance coverages relative to local 'coordination of benefit' rules. One coverage (and only one) with 'focal=true' is to be used in the adjudication of this claim. Coverages appearing before the focal Coverage in the list, and where 'Coverage.subrogation=false', should provide a reference to the ClaimResponse containing the adjudication results of the prior claim.
 */
export interface ClaimInsurance extends BackboneElement {
  /**
   * A business agreement number established between the provider and the insurer for special business processing purposes.
   */
  businessArrangement?: string;
  _businessArrangement?: Element;
  /**
   * Must not be specified when 'focal=true' for this insurance.
   */
  claimResponse?: Reference;
  /**
   * Reference to the insurance card level information contained in the Coverage resource. The coverage issuing insurer will use these details to locate the patient's actual coverage within the insurer's information system.
   */
  coverage: Reference;
  /**
   * A patient may (will) have multiple insurance policies which provide reimbursement for healthcare services and products. For example a person may also be covered by their spouse's policy and both appear in the list (and may be from the same insurer). This flag will be set to true for only one of the listed policies and that policy will be used for adjudicating this claim. Other claims would be created to request adjudication against the other listed policies.
   */
  focal: boolean;
  _focal?: Element;
  /**
   * Only required in jurisdictions where insurers, rather than the provider, are required to send claims to  insurers that appear after them in the list. This element is not required when 'subrogation=true'.
   */
  identifier?: Identifier;
  /**
   * This value is an alphanumeric string that may be provided over the phone, via text, via paper, or within a ClaimResponse resource and is not a FHIR Identifier.
   */
  preAuthRef?: string[];
  _preAuthRef?: Element;
  /**
   * A number to uniquely identify insurance entries and provide a sequence of coverages to convey coordination of benefit order.
   */
  sequence: number;
}
/**
 * Details of an accident which resulted in injuries which required the products and services listed in the claim.
 */
export interface ClaimAccident extends BackboneElement {
  /**
   * The date of the accident has to precede the dates of the products and services but within a reasonable timeframe.
   */
  date: string;
  _date?: Element;
  /**
   * The physical location of the accident event.
   */
  locationAddress?: Address;
  /**
   * The physical location of the accident event.
   */
  locationReference?: Reference;
  /**
   * The type or context of the accident event for the purposes of selection of potential insurance coverages and determination of coordination between insurers.
   */
  type?: CodeableConcept;
}
/**
 * A claim detail line. Either a simple (a product or service) or a 'group' of sub-details which are simple items.
 */
export interface ClaimItemDetailSubDetail extends BackboneElement {
  /**
   * Examples include Medical Care, Periodontics, Renal Dialysis, Vision Coverage.
   */
  category?: CodeableConcept;
  /**
   * To show a 10% senior's discount, the value entered is: 0.90 (1.00 - 0.10).
   */
  factor?: number;
  /**
   * For example in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or out of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * For example, the formula: quantity * unitPrice * factor  = net. Quantity and factor are assumed to be 1 if not supplied.
   */
  net?: Money;
  /**
   * If this is an actual service or product line, i.e. not a Group, then use code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI). If a grouping item then use a group code to indicate the type of thing being grouped e.g. 'glasses' or 'compound'.
   */
  productOrService: CodeableConcept;
  /**
   * For example: Neonatal program, child dental program or drug users recovery program.
   */
  programCode?: CodeableConcept[];
  /**
   * The number of repetitions of a service or product.
   */
  quantity?: Quantity;
  /**
   * The type of revenue or cost center providing the product and/or service.
   */
  revenue?: CodeableConcept;
  /**
   * A number to uniquely identify item entries.
   */
  sequence: number;
  /**
   * Unique Device Identifiers associated with this line item.
   */
  udi?: Reference[];
  /**
   * If the item is not a group then this is the fee for the product or service, otherwise this is the total of the fees for the details of the group.
   */
  unitPrice?: Money;
}
/**
 * A claim detail line. Either a simple (a product or service) or a 'group' of sub-details which are simple items.
 */
export interface ClaimItemDetail extends BackboneElement {
  /**
   * Examples include Medical Care, Periodontics, Renal Dialysis, Vision Coverage.
   */
  category?: CodeableConcept;
  /**
   * To show a 10% senior's discount, the value entered is: 0.90 (1.00 - 0.10).
   */
  factor?: number;
  /**
   * For example in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or out of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * For example, the formula: quantity * unitPrice * factor  = net. Quantity and factor are assumed to be 1 if not supplied.
   */
  net?: Money;
  /**
   * If this is an actual service or product line, i.e. not a Group, then use code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI). If a grouping item then use a group code to indicate the type of thing being grouped e.g. 'glasses' or 'compound'.
   */
  productOrService: CodeableConcept;
  /**
   * For example: Neonatal program, child dental program or drug users recovery program.
   */
  programCode?: CodeableConcept[];
  /**
   * The number of repetitions of a service or product.
   */
  quantity?: Quantity;
  /**
   * The type of revenue or cost center providing the product and/or service.
   */
  revenue?: CodeableConcept;
  /**
   * A number to uniquely identify item entries.
   */
  sequence: number;
  /**
   * A claim detail line. Either a simple (a product or service) or a 'group' of sub-details which are simple items.
   */
  subDetail?: ClaimItemDetailSubDetail[];
  /**
   * Unique Device Identifiers associated with this line item.
   */
  udi?: Reference[];
  /**
   * If the item is not a group then this is the fee for the product or service, otherwise this is the total of the fees for the details of the group.
   */
  unitPrice?: Money;
}
/**
 * A claim line. Either a simple  product or service or a 'group' of details which can each be a simple items or groups of sub-details.
 */
export interface ClaimItem extends BackboneElement {
  /**
   * For example: Providing a tooth code, allows an insurer to identify a provider performing a filling on a tooth that was previously removed.
   */
  bodySite?: CodeableConcept;
  /**
   * CareTeam members related to this service or product.
   */
  careTeamSequence?: number[];
  /**
   * Examples include Medical Care, Periodontics, Renal Dialysis, Vision Coverage.
   */
  category?: CodeableConcept;
  /**
   * A claim detail line. Either a simple (a product or service) or a 'group' of sub-details which are simple items.
   */
  detail?: ClaimItemDetail[];
  /**
   * Diagnosis applicable for this service or product.
   */
  diagnosisSequence?: number[];
  /**
   * This will typically be the encounter the event occurred within, but some activities may be initiated prior to or after the official completion of an encounter but still be tied to the context of the encounter.
   */
  encounter?: Reference[];
  /**
   * To show a 10% senior's discount, the value entered is: 0.90 (1.00 - 0.10).
   */
  factor?: number;
  /**
   * Exceptions, special conditions and supporting information applicable for this service or product.
   */
  informationSequence?: number[];
  /**
   * Where the product or service was provided.
   */
  locationCodeableConcept?: CodeableConcept;
  /**
   * Where the product or service was provided.
   */
  locationAddress?: Address;
  /**
   * Where the product or service was provided.
   */
  locationReference?: Reference;
  /**
   * For example in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or outside of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * For example, the formula: quantity * unitPrice * factor  = net. Quantity and factor are assumed to be 1 if not supplied.
   */
  net?: Money;
  /**
   * Procedures applicable for this service or product.
   */
  procedureSequence?: number[];
  /**
   * If this is an actual service or product line, i.e. not a Group, then use code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI). If a grouping item then use a group code to indicate the type of thing being grouped e.g. 'glasses' or 'compound'.
   */
  productOrService: CodeableConcept;
  /**
   * For example: Neonatal program, child dental program or drug users recovery program.
   */
  programCode?: CodeableConcept[];
  /**
   * The number of repetitions of a service or product.
   */
  quantity?: Quantity;
  /**
   * The type of revenue or cost center providing the product and/or service.
   */
  revenue?: CodeableConcept;
  /**
   * A number to uniquely identify item entries.
   */
  sequence: number;
  /**
   * The date or dates when the service or product was supplied, performed or completed.
   */
  servicedDate?: string;
  _servicedDate?: Element;
  /**
   * The date or dates when the service or product was supplied, performed or completed.
   */
  servicedPeriod?: Period;
  /**
   * A region or surface of the bodySite, e.g. limb region or tooth surface(s).
   */
  subSite?: CodeableConcept[];
  /**
   * Unique Device Identifiers associated with this line item.
   */
  udi?: Reference[];
  /**
   * If the item is not a group then this is the fee for the product or service, otherwise this is the total of the fees for the details of the group.
   */
  unitPrice?: Money;
}
/**
 * A provider issued list of professional services and products which have been provided, or are to be provided, to a patient which is sent to an insurer for reimbursement.
 */
export interface Claim extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Claim';
  /**
   * Details of an accident which resulted in injuries which required the products and services listed in the claim.
   */
  accident?: ClaimAccident;
  /**
   * Typically this would be today or in the past for a claim, and today or in the future for preauthorizations and predeterminations. Typically line item dates of service should fall within the billing period if one is specified.
   */
  billablePeriod?: Period;
  /**
   * The members of the team who provided the products and services.
   */
  careTeam?: ClaimCareTeam[];
  /**
   * This field is independent of the date of creation of the resource as it may reflect the creation date of a source document prior to digitization. Typically for claims all services must be completed as of this date.
   */
  created: string;
  _created?: Element;
  /**
   * Information about diagnoses relevant to the claim items.
   */
  diagnosis?: ClaimDiagnosis[];
  /**
   * Individual who created the claim, predetermination or preauthorization.
   */
  enterer?: Reference;
  /**
   * Facility where the services were provided.
   */
  facility?: Reference;
  /**
   * This field is only used for preauthorizations.
   */
  fundsReserve?: CodeableConcept;
  /**
   * A unique identifier assigned to this claim.
   */
  identifier?: Identifier[];
  /**
   * All insurance coverages for the patient which may be applicable for reimbursement, of the products and services listed in the claim, are typically provided in the claim to allow insurers to confirm the ordering of the insurance coverages relative to local 'coordination of benefit' rules. One coverage (and only one) with 'focal=true' is to be used in the adjudication of this claim. Coverages appearing before the focal Coverage in the list, and where 'Coverage.subrogation=false', should provide a reference to the ClaimResponse containing the adjudication results of the prior claim.
   */
  insurance: ClaimInsurance[];
  /**
   * The Insurer who is target of the request.
   */
  insurer?: Reference;
  /**
   * A claim line. Either a simple  product or service or a 'group' of details which can each be a simple items or groups of sub-details.
   */
  item?: ClaimItem[];
  /**
   * For example, a physician may prescribe a medication which the pharmacy determines is contraindicated, or for which the patient has an intolerance, and therefore issues a new prescription for an alternate medication which has the same therapeutic intent. The prescription from the pharmacy becomes the 'prescription' and that from the physician becomes the 'original prescription'.
   */
  originalPrescription?: Reference;
  /**
   * The party to whom the professional services and/or products have been supplied or are being considered and for whom actual or forecast reimbursement is sought.
   */
  patient: Reference;
  /**
   * Often providers agree to receive the benefits payable to reduce the near-term costs to the patient. The insurer may decline to pay the provider and choose to pay the subscriber instead.
   */
  payee?: ClaimPayee;
  /**
   * Prescription to support the dispensing of pharmacy, device or vision products.
   */
  prescription?: Reference;
  /**
   * If a claim processor is unable to complete the processing as per the priority then they should generate and error and not process the request.
   */
  priority: CodeableConcept;
  /**
   * Procedures performed on the patient relevant to the billing items with the claim.
   */
  procedure?: ClaimProcedure[];
  /**
   * Typically this field would be 1..1 where this party is responsible for the claim but not necessarily professionally responsible for the provision of the individual products and services listed below.
   */
  provider: Reference;
  /**
   * The referral resource which lists the date, practitioner, reason and other supporting information.
   */
  referral?: Reference;
  /**
   * For example,  for the original treatment and follow-up exams.
   */
  related?: ClaimRelated[];
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * This may contain the local bill type codes, for example the US UB-04 bill type code or the CMS bill type.
   */
  subType?: CodeableConcept;
  /**
   * Often there are multiple jurisdiction specific valuesets which are required.
   */
  supportingInfo?: ClaimSupportingInfo[];
  /**
   * The total value of the all the items in the claim.
   */
  total?: Money;
  /**
   * The majority of jurisdictions use: oral, pharmacy, vision, professional and institutional, or variants on those terms, as the general styles of claims. The valueset is extensible to accommodate other jurisdictional requirements.
   */
  type: CodeableConcept;
  /**
   * A code to indicate whether the nature of the request is: to request adjudication of products and services previously rendered; or requesting authorization and adjudication for provision in the future; or requesting the non-binding adjudication of the listed products and services which could be provided in the future.
   */
  use: string;
  _use?: Element;
}
/**
 * Code Values for the Claim.status field
 */
export enum ClaimStatusCodes {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  DRAFT = 'draft',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Code Values for the Claim.use field
 */
export enum ClaimUseCodes {
  CLAIM = 'claim',
  PREAUTHORIZATION = 'preauthorization',
  PREDETERMINATION = 'predetermination',
}
/**
 * If this item is a group then the values here are a summary of the adjudication of the detail items. If this item is a simple product or service then this is the result of the adjudication of this item.
 */
export interface ClaimResponseItemAdjudication extends BackboneElement {
  /**
   * For example: amount submitted, eligible amount, co-payment, and benefit payable.
   */
  amount?: Money;
  /**
   * For example codes indicating: Co-Pay, deductible, eligible, benefit, tax, etc.
   */
  category: CodeableConcept;
  /**
   * For example may indicate that the funds for this benefit type have been exhausted.
   */
  reason?: CodeableConcept;
  /**
   * For example: eligible percentage or co-payment percentage.
   */
  value?: number;
}
/**
 * A sub-detail adjudication of a simple product or service.
 */
export interface ClaimResponseItemDetailSubDetail extends BackboneElement {
  /**
   * The adjudication results.
   */
  adjudication?: ClaimResponseItemAdjudication[];
  /**
   * The numbers associated with notes below which apply to the adjudication of this item.
   */
  noteNumber?: number[];
  /**
   * A number to uniquely reference the claim sub-detail entry.
   */
  subDetailSequence: number;
}
/**
 * A claim detail. Either a simple (a product or service) or a 'group' of sub-details which are simple items.
 */
export interface ClaimResponseItemDetail extends BackboneElement {
  /**
   * The adjudication results.
   */
  adjudication: ClaimResponseItemAdjudication[];
  /**
   * A number to uniquely reference the claim detail entry.
   */
  detailSequence: number;
  /**
   * The numbers associated with notes below which apply to the adjudication of this item.
   */
  noteNumber?: number[];
  /**
   * A sub-detail adjudication of a simple product or service.
   */
  subDetail?: ClaimResponseItemDetailSubDetail[];
}
/**
 * A claim line. Either a simple (a product or service) or a 'group' of details which can also be a simple items or groups of sub-details.
 */
export interface ClaimResponseItem extends BackboneElement {
  /**
   * If this item is a group then the values here are a summary of the adjudication of the detail items. If this item is a simple product or service then this is the result of the adjudication of this item.
   */
  adjudication: ClaimResponseItemAdjudication[];
  /**
   * A claim detail. Either a simple (a product or service) or a 'group' of sub-details which are simple items.
   */
  detail?: ClaimResponseItemDetail[];
  /**
   * A number to uniquely reference the claim item entries.
   */
  itemSequence: number;
  /**
   * The numbers associated with notes below which apply to the adjudication of this item.
   */
  noteNumber?: number[];
}
/**
 * The third-tier service adjudications for payor added services.
 */
export interface ClaimResponseAddItemDetailSubDetail extends BackboneElement {
  /**
   * The adjudication results.
   */
  adjudication: ClaimResponseItemAdjudication[];
  /**
   * To show a 10% senior's discount, the value entered is: 0.90 (1.00 - 0.10).
   */
  factor?: number;
  /**
   * For example in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or outside of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * For example, the formula: quantity * unitPrice * factor  = net. Quantity and factor are assumed to be 1 if not supplied.
   */
  net?: Money;
  /**
   * The numbers associated with notes below which apply to the adjudication of this item.
   */
  noteNumber?: number[];
  /**
   * If this is an actual service or product line, i.e. not a Group, then use code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI). If a grouping item then use a group code to indicate the type of thing being grouped e.g. 'glasses' or 'compound'.
   */
  productOrService: CodeableConcept;
  /**
   * The number of repetitions of a service or product.
   */
  quantity?: Quantity;
  /**
   * If the item is not a group then this is the fee for the product or service, otherwise this is the total of the fees for the details of the group.
   */
  unitPrice?: Money;
}
/**
 * The second-tier service adjudications for payor added services.
 */
export interface ClaimResponseAddItemDetail extends BackboneElement {
  /**
   * The adjudication results.
   */
  adjudication: ClaimResponseItemAdjudication[];
  /**
   * To show a 10% senior's discount, the value entered is: 0.90 (1.00 - 0.10).
   */
  factor?: number;
  /**
   * For example in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or outside of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * For example, the formula: quantity * unitPrice * factor  = net. Quantity and factor are assumed to be 1 if not supplied.
   */
  net?: Money;
  /**
   * The numbers associated with notes below which apply to the adjudication of this item.
   */
  noteNumber?: number[];
  /**
   * If this is an actual service or product line, i.e. not a Group, then use code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI). If a grouping item then use a group code to indicate the type of thing being grouped e.g. 'glasses' or 'compound'.
   */
  productOrService: CodeableConcept;
  /**
   * The number of repetitions of a service or product.
   */
  quantity?: Quantity;
  /**
   * The third-tier service adjudications for payor added services.
   */
  subDetail?: ClaimResponseAddItemDetailSubDetail[];
  /**
   * If the item is not a group then this is the fee for the product or service, otherwise this is the total of the fees for the details of the group.
   */
  unitPrice?: Money;
}
/**
 * The first-tier service adjudications for payor added product or service lines.
 */
export interface ClaimResponseAddItem extends BackboneElement {
  /**
   * The adjudication results.
   */
  adjudication: ClaimResponseItemAdjudication[];
  /**
   * For example: Providing a tooth code allows an insurer to identify a provider performing a filling on a tooth that was previously removed.
   */
  bodySite?: CodeableConcept;
  /**
   * The second-tier service adjudications for payor added services.
   */
  detail?: ClaimResponseAddItemDetail[];
  /**
   * The sequence number of the details within the claim item which this line is intended to replace.
   */
  detailSequence?: number[];
  /**
   * To show a 10% senior's discount, the value entered is: 0.90 (1.00 - 0.10).
   */
  factor?: number;
  /**
   * Claim items which this service line is intended to replace.
   */
  itemSequence?: number[];
  /**
   * Where the product or service was provided.
   */
  locationCodeableConcept?: CodeableConcept;
  /**
   * Where the product or service was provided.
   */
  locationAddress?: Address;
  /**
   * Where the product or service was provided.
   */
  locationReference?: Reference;
  /**
   * For example in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or outside of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * For example, the formula: quantity * unitPrice * factor  = net. Quantity and factor are assumed to be 1 if not supplied.
   */
  net?: Money;
  /**
   * The numbers associated with notes below which apply to the adjudication of this item.
   */
  noteNumber?: number[];
  /**
   * If this is an actual service or product line, i.e. not a Group, then use code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI). If a grouping item then use a group code to indicate the type of thing being grouped e.g. 'glasses' or 'compound'.
   */
  productOrService: CodeableConcept;
  /**
   * For example: Neonatal program, child dental program or drug users recovery program.
   */
  programCode?: CodeableConcept[];
  /**
   * The providers who are authorized for the services rendered to the patient.
   */
  provider?: Reference[];
  /**
   * The number of repetitions of a service or product.
   */
  quantity?: Quantity;
  /**
   * The date or dates when the service or product was supplied, performed or completed.
   */
  servicedDate?: string;
  _servicedDate?: Element;
  /**
   * The date or dates when the service or product was supplied, performed or completed.
   */
  servicedPeriod?: Period;
  /**
   * The sequence number of the sub-details within the details within the claim item which this line is intended to replace.
   */
  subdetailSequence?: number[];
  /**
   * A region or surface of the bodySite, e.g. limb region or tooth surface(s).
   */
  subSite?: CodeableConcept[];
  /**
   * If the item is not a group then this is the fee for the product or service, otherwise this is the total of the fees for the details of the group.
   */
  unitPrice?: Money;
}
/**
 * Totals for amounts submitted, co-pays, benefits payable etc.
 */
export interface ClaimResponseTotal extends BackboneElement {
  /**
   * Monetary total amount associated with the category.
   */
  amount: Money;
  /**
   * For example codes indicating: Co-Pay, deductible, eligible, benefit, tax, etc.
   */
  category: CodeableConcept;
}
/**
 * Payment details for the adjudication of the claim.
 */
export interface ClaimResponsePayment extends BackboneElement {
  /**
   * Insurers will deduct amounts owing from the provider (adjustment), such as a prior overpayment, from the amount owing to the provider (benefits payable) when payment is made to the provider.
   */
  adjustment?: Money;
  /**
   * Reason for the payment adjustment.
   */
  adjustmentReason?: CodeableConcept;
  /**
   * Benefits payable less any payment adjustment.
   */
  amount: Money;
  /**
   * Estimated date the payment will be issued or the actual issue date of payment.
   */
  date?: string;
  _date?: Element;
  /**
   * For example: EFT number or check number.
   */
  identifier?: Identifier;
  /**
   * Whether this represents partial or complete payment of the benefits payable.
   */
  type: CodeableConcept;
}
/**
 * A note that describes or explains adjudication results in a human readable form.
 */
export interface ClaimResponseProcessNote extends BackboneElement {
  /**
   * Only required if the language is different from the resource language.
   */
  language?: CodeableConcept;
  /**
   * A number to uniquely identify a note entry.
   */
  number?: number;
  /**
   * The explanation or description associated with the processing.
   */
  text: string;
  _text?: Element;
  /**
   * The business purpose of the note text.
   */
  type?: string;
  _type?: Element;
}
/**
 * Code Values for the ClaimResponse.processNote.type field
 */
export enum ClaimResponseProcessNoteTypeCodes {
  DISPLAY = 'display',
  PRINT = 'print',
  PRINTOPER = 'printoper',
}
/**
 * All insurance coverages for the patient which may be applicable for reimbursement, of the products and services listed in the claim, are typically provided in the claim to allow insurers to confirm the ordering of the insurance coverages relative to local 'coordination of benefit' rules. One coverage (and only one) with 'focal=true' is to be used in the adjudication of this claim. Coverages appearing before the focal Coverage in the list, and where 'subrogation=false', should provide a reference to the ClaimResponse containing the adjudication results of the prior claim.
 */
export interface ClaimResponseInsurance extends BackboneElement {
  /**
   * A business agreement number established between the provider and the insurer for special business processing purposes.
   */
  businessArrangement?: string;
  _businessArrangement?: Element;
  /**
   * Must not be specified when 'focal=true' for this insurance.
   */
  claimResponse?: Reference;
  /**
   * Reference to the insurance card level information contained in the Coverage resource. The coverage issuing insurer will use these details to locate the patient's actual coverage within the insurer's information system.
   */
  coverage: Reference;
  /**
   * A patient may (will) have multiple insurance policies which provide reimbursement for healthcare services and products. For example a person may also be covered by their spouse's policy and both appear in the list (and may be from the same insurer). This flag will be set to true for only one of the listed policies and that policy will be used for adjudicating this claim. Other claims would be created to request adjudication against the other listed policies.
   */
  focal: boolean;
  _focal?: Element;
  /**
   * A number to uniquely identify insurance entries and provide a sequence of coverages to convey coordination of benefit order.
   */
  sequence: number;
}
/**
 * If the request contains errors then an error element should be provided and no adjudication related sections (item, addItem, or payment) should be present.
 */
export interface ClaimResponseError extends BackboneElement {
  /**
   * An error code, from a specified code system, which details why the claim could not be adjudicated.
   */
  code: CodeableConcept;
  /**
   * The sequence number of the detail within the line item submitted which contains the error. This value is omitted when the error occurs outside of the item structure.
   */
  detailSequence?: number;
  /**
   * The sequence number of the line item submitted which contains the error. This value is omitted when the error occurs outside of the item structure.
   */
  itemSequence?: number;
  /**
   * The sequence number of the sub-detail within the detail within the line item submitted which contains the error. This value is omitted when the error occurs outside of the item structure.
   */
  subDetailSequence?: number;
}
/**
 * This resource provides the adjudication details from the processing of a Claim resource.
 */
export interface ClaimResponse extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ClaimResponse';
  /**
   * The first-tier service adjudications for payor added product or service lines.
   */
  addItem?: ClaimResponseAddItem[];
  /**
   * The adjudication results which are presented at the header level rather than at the line-item or add-item levels.
   */
  adjudication?: ClaimResponseItemAdjudication[];
  /**
   * For example: professional reports, documents, images, clinical resources, or accident reports.
   */
  communicationRequest?: Reference[];
  /**
   * The date this resource was created.
   */
  created: string;
  _created?: Element;
  /**
   * A human readable description of the status of the adjudication.
   */
  disposition?: string;
  _disposition?: Element;
  /**
   * If the request contains errors then an error element should be provided and no adjudication related sections (item, addItem, or payment) should be present.
   */
  error?: ClaimResponseError[];
  /**
   * Needed to permit insurers to include the actual form.
   */
  form?: Attachment;
  /**
   * May be needed to identify specific jurisdictional forms.
   */
  formCode?: CodeableConcept;
  /**
   * Fund would be release by a future claim quoting the preAuthRef of this response. Examples of values include: provider, patient, none.
   */
  fundsReserve?: CodeableConcept;
  /**
   * A unique identifier assigned to this claim response.
   */
  identifier?: Identifier[];
  /**
   * All insurance coverages for the patient which may be applicable for reimbursement, of the products and services listed in the claim, are typically provided in the claim to allow insurers to confirm the ordering of the insurance coverages relative to local 'coordination of benefit' rules. One coverage (and only one) with 'focal=true' is to be used in the adjudication of this claim. Coverages appearing before the focal Coverage in the list, and where 'subrogation=false', should provide a reference to the ClaimResponse containing the adjudication results of the prior claim.
   */
  insurance?: ClaimResponseInsurance[];
  /**
   * The party responsible for authorization, adjudication and reimbursement.
   */
  insurer: Reference;
  /**
   * A claim line. Either a simple (a product or service) or a 'group' of details which can also be a simple items or groups of sub-details.
   */
  item?: ClaimResponseItem[];
  /**
   * The resource may be used to indicate that: the request has been held (queued) for processing; that it has been processed and errors found (error); that no errors were found and that some of the adjudication has been undertaken (partial) or that all of the adjudication has been undertaken (complete).
   */
  outcome: string;
  _outcome?: Element;
  /**
   * The party to whom the professional services and/or products have been supplied or are being considered and for whom actual for facast reimbursement is sought.
   */
  patient: Reference;
  /**
   * Type of Party to be reimbursed: subscriber, provider, other.
   */
  payeeType?: CodeableConcept;
  /**
   * Payment details for the adjudication of the claim.
   */
  payment?: ClaimResponsePayment;
  /**
   * The time frame during which this authorization is effective.
   */
  preAuthPeriod?: Period;
  /**
   * This value is only present on preauthorization adjudications.
   */
  preAuthRef?: string;
  _preAuthRef?: Element;
  /**
   * A note that describes or explains adjudication results in a human readable form.
   */
  processNote?: ClaimResponseProcessNote[];
  /**
   * Original request resource reference.
   */
  request?: Reference;
  /**
   * Typically this field would be 1..1 where this party is responsible for the claim but not necessarily professionally responsible for the provision of the individual products and services listed below.
   */
  requestor?: Reference;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * This may contain the local bill type codes, for example the US UB-04 bill type code or the CMS bill type.
   */
  subType?: CodeableConcept;
  /**
   * Totals for amounts submitted, co-pays, benefits payable etc.
   */
  total?: ClaimResponseTotal[];
  /**
   * This may contain the local bill type codes, for example the US UB-04 bill type code or the CMS bill type.
   */
  type: CodeableConcept;
  /**
   * A code to indicate whether the nature of the request is: to request adjudication of products and services previously rendered; or requesting authorization and adjudication for provision in the future; or requesting the non-binding adjudication of the listed products and services which could be provided in the future.
   */
  use: string;
  _use?: Element;
}
/**
 * Code Values for the ClaimResponse.outcome field
 */
export enum ClaimResponseOutcomeCodes {
  QUEUED = 'queued',
  COMPLETE = 'complete',
  ERROR = 'error',
  PARTIAL = 'partial',
}
/**
 * Code Values for the ClaimResponse.status field
 */
export enum ClaimResponseStatusCodes {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  DRAFT = 'draft',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Code Values for the ClaimResponse.use field
 */
export enum ClaimResponseUseCodes {
  CLAIM = 'claim',
  PREAUTHORIZATION = 'preauthorization',
  PREDETERMINATION = 'predetermination',
}
/**
 * One or more sets of investigations (signs, symptoms, etc.). The actual grouping of investigations varies greatly depending on the type and context of the assessment. These investigations may include data generated during the assessment process, or data previously generated and recorded that is pertinent to the outcomes.
 */
export interface ClinicalImpressionInvestigation extends BackboneElement {
  /**
   * A name/code for the group ("set") of investigations. Typically, this will be something like "signs", "symptoms", "clinical", "diagnostic", but the list is not constrained, and others such groups such as (exposure|family|travel|nutritional) history may be used.
   */
  code: CodeableConcept;
  /**
   * Most investigations are observations of one kind or another but some other specific types of data collection resources can also be used.
   */
  item?: Reference[];
}
/**
 * Specific findings or diagnoses that were considered likely or relevant to ongoing treatment.
 */
export interface ClinicalImpressionFinding extends BackboneElement {
  /**
   * Which investigations support finding or diagnosis.
   */
  basis?: string;
  _basis?: Element;
  /**
   * Specific text or code for finding or diagnosis, which may include ruled-out or resolved conditions.
   */
  itemCodeableConcept?: CodeableConcept;
  /**
   * Specific reference for finding or diagnosis, which may include ruled-out or resolved conditions.
   */
  itemReference?: Reference;
}
/**
 * A record of a clinical assessment performed to determine what problem(s) may affect the patient and before planning the treatments or management strategies that are best to manage a patient's condition. Assessments are often 1:1 with a clinical consultation / encounter,  but this varies greatly depending on the clinical workflow. This resource is called "ClinicalImpression" rather than "ClinicalAssessment" to avoid confusion with the recording of assessment tools such as Apgar score.
 */
export interface ClinicalImpression extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ClinicalImpression';
  /**
   * The clinician performing the assessment.
   */
  assessor?: Reference;
  /**
   * This is present as a place-holder only and may be removed based on feedback/work group opinion.
   */
  code?: CodeableConcept;
  /**
   * Indicates when the documentation of the assessment was complete.
   */
  date?: string;
  _date?: Element;
  /**
   * A summary of the context and/or cause of the assessment - why / where it was performed, and what patient events/status prompted it.
   */
  description?: string;
  _description?: Element;
  /**
   * This SHOULD be accurate to at least the minute, though some assessments only have a known date.
   */
  effectiveDateTime?: string;
  _effectiveDateTime?: Element;
  /**
   * This SHOULD be accurate to at least the minute, though some assessments only have a known date.
   */
  effectivePeriod?: Period;
  /**
   * This will typically be the encounter the event occurred within, but some activities may be initiated prior to or after the official completion of an encounter but still be tied to the context of the encounter.
   */
  encounter?: Reference;
  /**
   * Specific findings or diagnoses that were considered likely or relevant to ongoing treatment.
   */
  finding?: ClinicalImpressionFinding[];
  /**
   * This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  identifier?: Identifier[];
  /**
   * One or more sets of investigations (signs, symptoms, etc.). The actual grouping of investigations varies greatly depending on the type and context of the assessment. These investigations may include data generated during the assessment process, or data previously generated and recorded that is pertinent to the outcomes.
   */
  investigation?: ClinicalImpressionInvestigation[];
  /**
   * Don't use this element for content that should more properly appear as one of the specific elements of the impression.
   */
  note?: Annotation[];
  /**
   * It is always likely that multiple previous assessments exist for a patient. The point of quoting a previous assessment is that this assessment is relative to it (see resolved).
   */
  previous?: Reference;
  /**
   * e.g. The patient is a pregnant, has congestive heart failure, has an ‎Adenocarcinoma, and is allergic to penicillin.
   */
  problem?: Reference[];
  /**
   * Estimate of likely outcome.
   */
  prognosisCodeableConcept?: CodeableConcept[];
  /**
   * RiskAssessment expressing likely outcome.
   */
  prognosisReference?: Reference[];
  /**
   * Reference to a specific published clinical protocol that was followed during this assessment, and/or that provides evidence in support of the diagnosis.
   */
  protocol?: string[];
  _protocol?: Element;
  /**
   * This element is labeled as a modifier because the status contains the code entered-in-error that marks the clinical impression as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * This is generally only used for "exception" statuses such as "not-done", "suspended" or "cancelled".
   * [distinct reason codes for different statuses can be enforced using invariants if they are universal bindings].
   */
  statusReason?: CodeableConcept;
  /**
   * The patient or group of individuals assessed as part of this record.
   */
  subject: Reference;
  /**
   * A text summary of the investigations and the diagnosis.
   */
  summary?: string;
  _summary?: Element;
  /**
   * Information supporting the clinical impression.
   */
  supportingInfo?: Reference[];
}
/**
 * Code Values for the ClinicalImpression.status field
 */
export enum ClinicalImpressionStatusCodes {
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Note that filters defined in code systems usually require custom code on the part of any terminology engine that will make them available for use in value set filters. For this reason, they are generally only seen in high value published terminologies.
 */
export interface CodeSystemFilter extends BackboneElement {
  /**
   * The code that identifies this filter when it is used as a filter in [ValueSet](valueset.html#).compose.include.filter.
   */
  code: string;
  _code?: Element;
  /**
   * A description of how or why the filter is used.
   */
  description?: string;
  _description?: Element;
  /**
   * A list of operators that can be used with the filter.
   */
  operator: string[];
  _operator?: Element;
  /**
   * A description of what the value for the filter should be.
   */
  value: string;
  _value?: Element;
}
/**
 * Code Values for the CodeSystem.filter.operator field
 */
export enum CodeSystemFilterOperatorCodes {
  EQUALS = '=',
  IS_A = 'is-a',
  DESCENDENT_OF = 'descendent-of',
  IS_NOT_A = 'is-not-a',
  REGEX = 'regex',
  IN = 'in',
  NOT_IN = 'not-in',
  GENERALIZES = 'generalizes',
  EXISTS = 'exists',
}
/**
 * A property defines an additional slot through which additional information can be provided about a concept.
 */
export interface CodeSystemProperty extends BackboneElement {
  /**
   * A code that is used to identify the property. The code is used internally (in CodeSystem.concept.property.code) and also externally, such as in property filters.
   */
  code: string;
  _code?: Element;
  /**
   * A description of the property- why it is defined, and how its value might be used.
   */
  description?: string;
  _description?: Element;
  /**
   * The type of the property value. Properties of type "code" contain a code defined by the code system (e.g. a reference to another defined concept).
   */
  type: string;
  _type?: Element;
  /**
   * Reference to the formal meaning of the property. One possible source of meaning is the [Concept Properties](codesystem-concept-properties.html) code system.
   */
  uri?: string;
  _uri?: Element;
}
/**
 * Code Values for the CodeSystem.property.type field
 */
export enum CodeSystemPropertyTypeCodes {
  CODE = 'code',
  CODING = 'Coding',
  STRING = 'string',
  INTEGER = 'integer',
  BOOLEAN = 'boolean',
  DATETIME = 'dateTime',
  DECIMAL = 'decimal',
}
/**
 * Concepts have both a ```display``` and an array of ```designation```. The display is equivalent to a special designation with an implied ```designation.use``` of "primary code" and a language equal to the [Resource Language](resource.html#language).
 */
export interface CodeSystemConceptDesignation extends BackboneElement {
  /**
   * In the absence of a language, the resource language applies.
   */
  language?: string;
  _language?: Element;
  /**
   * If no use is provided, the designation can be assumed to be suitable for general display to a human user.
   */
  use?: Coding;
  /**
   * The text value for this designation.
   */
  value: string;
  _value?: Element;
}
/**
 * A property value for this concept.
 */
export interface CodeSystemConceptProperty extends BackboneElement {
  /**
   * A code that is a reference to CodeSystem.property.code.
   */
  code: string;
  _code?: Element;
  /**
   * The value of this property.
   */
  valueCode: string;
  _valueCode?: Element;
  /**
   * The value of this property.
   */
  valueCoding: Coding;
  /**
   * The value of this property.
   */
  valueString: string;
  _valueString?: Element;
  /**
   * The value of this property.
   */
  valueInteger: number;
  /**
   * The value of this property.
   */
  valueBoolean: boolean;
  _valueBoolean?: Element;
  /**
   * The value of this property.
   */
  valueDateTime: string;
  _valueDateTime?: Element;
  /**
   * The value of this property.
   */
  valueDecimal: number;
}
/**
 * If this is empty, it means that the code system resource does not represent the content of the code system.
 */
export interface CodeSystemConcept extends BackboneElement {
  /**
   * A code - a text symbol - that uniquely identifies the concept within the code system.
   */
  code: string;
  _code?: Element;
  /**
   * Defines children of a concept to produce a hierarchy of concepts. The nature of the relationships is variable (is-a/contains/categorizes) - see hierarchyMeaning.
   */
  concept?: CodeSystemConcept[];
  /**
   * The formal definition of the concept. The code system resource does not make formal definitions required, because of the prevalence of legacy systems. However, they are highly recommended, as without them there is no formal meaning associated with the concept.
   */
  definition?: string;
  _definition?: Element;
  /**
   * Concepts have both a ```display``` and an array of ```designation```. The display is equivalent to a special designation with an implied ```designation.use``` of "primary code" and a language equal to the [Resource Language](resource.html#language).
   */
  designation?: CodeSystemConceptDesignation[];
  /**
   * A human readable string that is the recommended default way to present this concept to a user.
   */
  display?: string;
  _display?: Element;
  /**
   * A property value for this concept.
   */
  property?: CodeSystemConceptProperty[];
}
/**
 * The CodeSystem resource is used to declare the existence of and describe a code system or code system supplement and its key properties, and optionally define a part or all of its content.
 */
export interface CodeSystem extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'CodeSystem';
  /**
   * If this value is missing, then it is not specified whether a code system is case sensitive or not. When the rule is not known, Postel's law should be followed: produce codes with the correct case, and accept codes in any case. This element is primarily provided to support validation software.
   */
  caseSensitive?: boolean;
  _caseSensitive?: Element;
  /**
   * Note that the code system resource does not define what the compositional grammar is, only whether or not there is one.
   */
  compositional?: boolean;
  _compositional?: Element;
  /**
   * If this is empty, it means that the code system resource does not represent the content of the code system.
   */
  concept?: CodeSystemConcept[];
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * The extent of the content of the code system (the concepts and codes it defines) are represented in this resource instance.
   */
  content: string;
  _content?: Element;
  /**
   * ... Sometimes, the copyright differs between the code system and the codes that are included. The copyright statement should clearly differentiate between these when required.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * The count of concepts defined in this resource cannot be more than this value but may be less for several reasons - see the content element.
   */
  count?: number;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the code system. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the code system was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the code system as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the code system is presumed to be the predominant language in the place the code system was created).
   */
  description?: string;
  _description?: Element;
  /**
   * Allows filtering of code systems that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Note that filters defined in code systems usually require custom code on the part of any terminology engine that will make them available for use in value set filters. For this reason, they are generally only seen in high value published terminologies.
   */
  filter?: CodeSystemFilter[];
  /**
   * Note that other representations might have a different hierarchy or none at all, and represent the information using properties.
   */
  hierarchyMeaning?: string;
  _hierarchyMeaning?: Element;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this code system outside of FHIR, where it is not possible to use the logical URI.  Note that HL7 defines at least three identifiers for many of its code systems - the FHIR canonical URL, the OID and the V2 Table 0396 mnemonic code.
   */
  identifier?: Identifier[];
  /**
   * It may be possible for the code system to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * A property defines an additional slot through which additional information can be provided about a concept.
   */
  property?: CodeSystemProperty[];
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the code system is the organization or individual primarily responsible for the maintenance and upkeep of the code system. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the code system. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the code system. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this code system.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Allows filtering of code systems that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * The most common use of a code system supplement is to add additional language support.
   */
  supplements?: string;
  _supplements?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * The definition of the value set SHALL include all codes from this code system and only codes from this code system, and it SHALL be immutable.
   */
  valueSet?: string;
  _valueSet?: Element;
  /**
   * There may be different code system instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the code system with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
  /**
   * Best practice is that code systems do not redefine concepts, or that if concepts are redefined, a new code system definition is created. But this is not always possible, so some code systems may be defined as 'versionNeeded'.
   * Most code systems occasionally refine the displays defined for concepts between versions. Contexts in which the concept display values are validated may require that the version be specified for some code systems irrespective of the value of this property.
   */
  versionNeeded?: boolean;
  _versionNeeded?: Element;
}
/**
 * Code Values for the CodeSystem.content field
 */
export enum CodeSystemContentCodes {
  NOT_PRESENT = 'not-present',
  EXAMPLE = 'example',
  FRAGMENT = 'fragment',
  COMPLETE = 'complete',
  SUPPLEMENT = 'supplement',
}
/**
 * Code Values for the CodeSystem.hierarchyMeaning field
 */
export enum CodeSystemHierarchyMeaningCodes {
  GROUPED_BY = 'grouped-by',
  IS_A = 'is-a',
  PART_OF = 'part-of',
  CLASSIFIED_WITH = 'classified-with',
}
/**
 * Code Values for the CodeSystem.status field
 */
export enum CodeSystemStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Text, attachment(s), or resource(s) that was communicated to the recipient.
 */
export interface CommunicationPayload extends BackboneElement {
  /**
   * A communicated content (or for multi-part communications, one portion of the communication).
   */
  contentString: string;
  _contentString?: Element;
  /**
   * A communicated content (or for multi-part communications, one portion of the communication).
   */
  contentAttachment: Attachment;
  /**
   * A communicated content (or for multi-part communications, one portion of the communication).
   */
  contentReference: Reference;
}
/**
 * An occurrence of information being transmitted; e.g. an alert that was sent to a responsible provider, a public health agency that was notified about a reportable condition.
 */
export interface Communication extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Communication';
  /**
   * Don't use Communication.about element when a more specific element exists, such as basedOn or reasonReference.
   */
  about?: Reference[];
  /**
   * This must point to some sort of a 'Request' resource, such as CarePlan, CommunicationRequest, ServiceRequest, MedicationRequest, etc.
   */
  basedOn?: Reference[];
  /**
   * There may be multiple axes of categorization and one communication may serve multiple purposes.
   */
  category?: CodeableConcept[];
  /**
   * This will typically be the encounter the event occurred within, but some activities may be initiated prior to or after the official completion of an encounter but still be tied to the context of the encounter.
   */
  encounter?: Reference;
  /**
   * This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  identifier?: Identifier[];
  /**
   * Prior communication that this communication is in response to.
   */
  inResponseTo?: Reference[];
  /**
   * The URL pointing to a FHIR-defined protocol, guideline, orderset or other definition that is adhered to in whole or in part by this Communication.
   */
  instantiatesCanonical?: string[];
  _instantiatesCanonical?: Element;
  /**
   * This might be an HTML page, PDF, etc. or could just be a non-resolvable URI identifier.
   */
  instantiatesUri?: string[];
  _instantiatesUri?: Element;
  /**
   * A channel that was used for this communication (e.g. email, fax).
   */
  medium?: CodeableConcept[];
  /**
   * Additional notes or commentary about the communication by the sender, receiver or other interested parties.
   */
  note?: Annotation[];
  /**
   * Part of this action.
   */
  partOf?: Reference[];
  /**
   * Text, attachment(s), or resource(s) that was communicated to the recipient.
   */
  payload?: CommunicationPayload[];
  /**
   * Used to prioritize workflow (such as which communication to read first) when the communication is planned or in progress.
   */
  priority?: string;
  _priority?: Element;
  /**
   * Textual reasons can be captured using reasonCode.text.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Indicates another resource whose existence justifies this communication.
   */
  reasonReference?: Reference[];
  /**
   * The time when this communication arrived at the destination.
   */
  received?: string;
  _received?: Element;
  /**
   * The entity (e.g. person, organization, clinical information system, care team or device) which was the target of the communication. If receipts need to be tracked by an individual, a separate resource instance will need to be created for each recipient.  Multiple recipient communications are intended where either receipts are not tracked (e.g. a mass mail-out) or a receipt is captured in aggregate (all emails confirmed received by a particular time).
   */
  recipient?: Reference[];
  /**
   * The entity (e.g. person, organization, clinical information system, or device) which was the source of the communication.
   */
  sender?: Reference;
  /**
   * The time when this communication was sent.
   */
  sent?: string;
  _sent?: Element;
  /**
   * This element is labeled as a modifier because the status contains the codes aborted and entered-in-error that mark the communication as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * This is generally only used for "exception" statuses such as "not-done", "suspended" or "aborted". The reason for performing the event at all is captured in reasonCode, not here.
   */
  statusReason?: CodeableConcept;
  /**
   * The patient or group that was the focus of this communication.
   */
  subject?: Reference;
  /**
   * Communication.topic.text can be used without any codings.
   */
  topic?: CodeableConcept;
}
/**
 * Code Values for the Communication.priority field
 */
export enum CommunicationPriorityCodes {
  ROUTINE = 'routine',
  URGENT = 'urgent',
  ASAP = 'asap',
  STAT = 'stat',
}
/**
 * Code Values for the Communication.status field
 */
export enum CommunicationStatusCodes {
  PREPARATION = 'preparation',
  IN_PROGRESS = 'in-progress',
  NOT_DONE = 'not-done',
  ON_HOLD = 'on-hold',
  STOPPED = 'stopped',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  UNKNOWN = 'unknown',
}
/**
 * Text, attachment(s), or resource(s) to be communicated to the recipient.
 */
export interface CommunicationRequestPayload extends BackboneElement {
  /**
   * The communicated content (or for multi-part communications, one portion of the communication).
   */
  contentString: string;
  _contentString?: Element;
  /**
   * The communicated content (or for multi-part communications, one portion of the communication).
   */
  contentAttachment: Attachment;
  /**
   * The communicated content (or for multi-part communications, one portion of the communication).
   */
  contentReference: Reference;
}
/**
 * A request to convey information; e.g. the CDS system proposes that an alert be sent to a responsible provider, the CDS system proposes that the public health agency be notified about a reportable condition.
 */
export interface CommunicationRequest extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'CommunicationRequest';
  /**
   * Don't use CommunicationRequest.about element when a more specific element exists, such as basedOn, reasonReference, or replaces.
   */
  about?: Reference[];
  /**
   * For draft requests, indicates the date of initial creation.  For requests with other statuses, indicates the date of activation.
   */
  authoredOn?: string;
  _authoredOn?: Element;
  /**
   * A plan or proposal that is fulfilled in whole or in part by this request.
   */
  basedOn?: Reference[];
  /**
   * There may be multiple axes of categorization and one communication request may serve multiple purposes.
   */
  category?: CodeableConcept[];
  /**
   * The attributes provided with the request qualify what is not to be done.
   */
  doNotPerform?: boolean;
  _doNotPerform?: Element;
  /**
   * This will typically be the encounter the event occurred within, but some activities may be initiated prior to or after the official completion of an encounter but still be tied to the context of the encounter.
   */
  encounter?: Reference;
  /**
   * Requests are linked either by a "basedOn" relationship (i.e. one request is fulfilling another) or by having a common requisition.  Requests that are part of the same requisition are generally treated independently from the perspective of changing their state or maintaining them after initial creation.
   */
  groupIdentifier?: Identifier;
  /**
   * This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  identifier?: Identifier[];
  /**
   * A channel that was used for this communication (e.g. email, fax).
   */
  medium?: CodeableConcept[];
  /**
   * Comments made about the request by the requester, sender, recipient, subject or other participants.
   */
  note?: Annotation[];
  /**
   * The time when this communication is to occur.
   */
  occurrenceDateTime?: string;
  _occurrenceDateTime?: Element;
  /**
   * The time when this communication is to occur.
   */
  occurrencePeriod?: Period;
  /**
   * Text, attachment(s), or resource(s) to be communicated to the recipient.
   */
  payload?: CommunicationRequestPayload[];
  /**
   * Characterizes how quickly the proposed act must be initiated. Includes concepts such as stat, urgent, routine.
   */
  priority?: string;
  _priority?: Element;
  /**
   * Textual reasons can be captured using reasonCode.text.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Indicates another resource whose existence justifies this request.
   */
  reasonReference?: Reference[];
  /**
   * The entity (e.g. person, organization, clinical information system, device, group, or care team) which is the intended target of the communication.
   */
  recipient?: Reference[];
  /**
   * The replacement could be because the initial request was immediately rejected (due to an issue) or because the previous request was completed, but the need for the action described by the request remains ongoing.
   */
  replaces?: Reference[];
  /**
   * The device, individual, or organization who initiated the request and has responsibility for its activation.
   */
  requester?: Reference;
  /**
   * The entity (e.g. person, organization, clinical information system, or device) which is to be the source of the communication.
   */
  sender?: Reference;
  /**
   * The status of the proposal or order.
   */
  status: string;
  _status?: Element;
  /**
   * This is generally only used for "exception" statuses such as "suspended" or "cancelled".  The reason why the CommunicationRequest was created at all is captured in reasonCode, not here.  [distinct reason codes for different statuses can be enforced using invariants if they are universal bindings].
   */
  statusReason?: CodeableConcept;
  /**
   * The patient or group that is the focus of this communication request.
   */
  subject?: Reference;
}
/**
 * Code Values for the CommunicationRequest.priority field
 */
export enum CommunicationRequestPriorityCodes {
  ROUTINE = 'routine',
  URGENT = 'urgent',
  ASAP = 'asap',
  STAT = 'stat',
}
/**
 * Code Values for the CommunicationRequest.status field
 */
export enum CommunicationRequestStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  ON_HOLD = 'on-hold',
  REVOKED = 'revoked',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  UNKNOWN = 'unknown',
}
/**
 * Information about how a resource is related to the compartment.
 */
export interface CompartmentDefinitionResource extends BackboneElement {
  /**
   * The name of a resource supported by the server.
   */
  code: string;
  _code?: Element;
  /**
   * Additional documentation about the resource and compartment.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * If no search parameters are listed, then the resource is not linked to the compartment.
   */
  param?: string[];
  _param?: Element;
}
/**
 * A compartment definition that defines how resources are accessed on a server.
 */
export interface CompartmentDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'CompartmentDefinition';
  /**
   * Only the specification can define the compartments that can exist. Servers can choose to support them.
   */
  code: string;
  _code?: Element;
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the compartment definition. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the compartment definition was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the compartment definition as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the compartment definition is presumed to be the predominant language in the place the compartment definition was created).
   */
  description?: string;
  _description?: Element;
  /**
   * Allows filtering of compartment definitions that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.This is often the same as the code for the parameter, but does not need to be.
   */
  name: string;
  _name?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the compartment definition is the organization or individual primarily responsible for the maintenance and upkeep of the compartment definition. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the compartment definition. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the compartment definition. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this compartment definition.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Information about how a resource is related to the compartment.
   */
  resource?: CompartmentDefinitionResource[];
  /**
   * Servers may define and use compartments to manage logical access without implementing the compartment related syntax.
   */
  search: boolean;
  _search?: Element;
  /**
   * Allows filtering of compartment definitions that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different compartment definition instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the compartment definition with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the CompartmentDefinition.code field
 */
export enum CompartmentDefinitionCodeCodes {
  PATIENT = 'Patient',
  ENCOUNTER = 'Encounter',
  RELATEDPERSON = 'RelatedPerson',
  PRACTITIONER = 'Practitioner',
  DEVICE = 'Device',
}
/**
 * Code Values for the CompartmentDefinition.status field
 */
export enum CompartmentDefinitionStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Only list each attester once.
 */
export interface CompositionAttester extends BackboneElement {
  /**
   * The type of attestation the authenticator offers.
   */
  mode: string;
  _mode?: Element;
  /**
   * Who attested the composition in the specified way.
   */
  party?: Reference;
  /**
   * When the composition was attested by the party.
   */
  time?: string;
  _time?: Element;
}
/**
 * Code Values for the Composition.attester.mode field
 */
export enum CompositionAttesterModeCodes {
  PERSONAL = 'personal',
  PROFESSIONAL = 'professional',
  LEGAL = 'legal',
  OFFICIAL = 'official',
}
/**
 * A document is a version specific composition.
 */
export interface CompositionRelatesTo extends BackboneElement {
  /**
   * If this document appends another document, then the document cannot be fully understood without also accessing the referenced document.
   */
  code: string;
  _code?: Element;
  /**
   * The target composition/document of this relationship.
   */
  targetIdentifier: Identifier;
  /**
   * The target composition/document of this relationship.
   */
  targetReference: Reference;
}
/**
 * Code Values for the Composition.relatesTo.code field
 */
export enum CompositionRelatesToCodeCodes {
  REPLACES = 'replaces',
  TRANSFORMS = 'transforms',
  SIGNS = 'signs',
  APPENDS = 'appends',
}
/**
 * The event needs to be consistent with the type element, though can provide further information if desired.
 */
export interface CompositionEvent extends BackboneElement {
  /**
   * An event can further specialize the act inherent in the typeCode, such as where it is simply "Procedure Report" and the procedure was a "colonoscopy". If one or more eventCodes are included, they SHALL NOT conflict with the values inherent in the classCode, practiceSettingCode or typeCode, as such a conflict would create an ambiguous situation. This short list of codes is provided to be used as key words for certain types of queries.
   */
  code?: CodeableConcept[];
  /**
   * The description and/or reference of the event(s) being documented. For example, this could be used to document such a colonoscopy or an appendectomy.
   */
  detail?: Reference[];
  /**
   * The period of time covered by the documentation. There is no assertion that the documentation is a complete representation for this period, only that it documents events during this time.
   */
  period?: Period;
}
/**
 * The root of the sections that make up the composition.
 */
export interface CompositionSection extends BackboneElement {
  /**
   * Identifies who is responsible for the information in this section, not necessarily who typed it in.
   */
  author?: Reference[];
  /**
   * The code identifies the section for an automated processor of the document. This is particularly relevant when using profiles to control the structure of the document.
   * If the section has content (instead of sub-sections), the section.code does not change the meaning or interpretation of the resource that is the content of the section in the comments for the section.code.
   */
  code?: CodeableConcept;
  /**
   * The various reasons for an empty section make a significant interpretation to its interpretation. Note that this code is for use when the entire section content has been suppressed, and not for when individual items are omitted - implementers may consider using a text note or a flag on an entry in these cases.
   */
  emptyReason?: CodeableConcept;
  /**
   * If there are no entries in the list, an emptyReason SHOULD be provided.
   */
  entry?: Reference[];
  /**
   * Typically, sections in a doument are about the subject of the document, whether that is a  patient, or group of patients, location, or device, or whatever. For some kind of documents, some sections actually contain data about related entities. Typical examples are  a section in a newborn discharge summary concerning the mother, or family history documents, with a section about each family member, though there are many other examples.
   */
  focus?: Reference;
  /**
   * This element is labeled as a modifier because a change list must not be misunderstood as a complete list.
   */
  mode?: string;
  _mode?: Element;
  /**
   * Applications SHOULD render ordered lists in the order provided, but MAY allow users to re-order based on their own preferences as well. If there is no order specified, the order is unknown, though there may still be some order.
   */
  orderedBy?: CodeableConcept;
  /**
   * Nested sections are primarily used to help human readers navigate to particular portions of the document.
   */
  section?: CompositionSection[];
  /**
   * Document profiles may define what content should be represented in the narrative to ensure clinical safety.
   */
  text?: Narrative;
  /**
   * The title identifies the section for a human reader. The title must be consistent with the narrative of the resource that is the target of the section.content reference. Generally, sections SHOULD have titles, but in some documents, it is unnecessary or inappropriate. Typically, this is where a section has subsections that have their own adequately distinguishing title,  or documents that only have a single section. Most Implementation Guides will make section title to be a required element.
   */
  title?: string;
  _title?: Element;
}
/**
 * Code Values for the Composition.section.mode field
 */
export enum CompositionSectionModeCodes {
  WORKING = 'working',
  SNAPSHOT = 'snapshot',
  CHANGES = 'changes',
}
/**
 * A set of healthcare-related information that is assembled together into a single logical package that provides a single coherent statement of meaning, establishes its own context and that has clinical attestation with regard to who is making the statement. A Composition defines the structure and narrative content necessary for a document. However, a Composition alone does not constitute a document. Rather, the Composition must be the first entry in a Bundle where Bundle.type=document, and any other resources referenced from Composition must be included as subsequent entries in the Bundle (for example Patient, Practitioner, Encounter, etc.).
 */
export interface Composition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Composition';
  /**
   * Only list each attester once.
   */
  attester?: CompositionAttester[];
  /**
   * Identifies who is responsible for the information in the composition, not necessarily who typed it in.
   */
  author: Reference[];
  /**
   * This is a metadata field from [XDS/MHD](http://wiki.ihe.net/index.php?title=Mobile_access_to_Health_Documents_(MHD)).
   */
  category?: CodeableConcept[];
  /**
   * The exact use of this element, and enforcement and issues related to highly sensitive documents are out of scope for the base specification, and delegated to implementation profiles (see security section).  This element is labeled as a modifier because highly confidential documents must not be treated as if they are not.
   */
  confidentiality?: string;
  _confidentiality?: Element;
  /**
   * This is useful when documents are derived from a composition - provides guidance for how to get the latest version of the document. This is optional because this is sometimes not known by the authoring system, and can be inferred by context. However, it is important that this information be known when working with a derived document, so providing a custodian is encouraged.
   */
  custodian?: Reference;
  /**
   * The Last Modified Date on the composition may be after the date of the document was attested without being changed.
   */
  date: string;
  _date?: Element;
  /**
   * Describes the clinical encounter or type of care this documentation is associated with.
   */
  encounter?: Reference;
  /**
   * The event needs to be consistent with the type element, though can provide further information if desired.
   */
  event?: CompositionEvent[];
  /**
   * Similar to ClinicalDocument/setId in CDA. See discussion in resource definition for how these relate.
   */
  identifier?: Identifier;
  /**
   * A document is a version specific composition.
   */
  relatesTo?: CompositionRelatesTo[];
  /**
   * The root of the sections that make up the composition.
   */
  section?: CompositionSection[];
  /**
   * If a composition is marked as withdrawn, the compositions/documents in the series, or data from the composition or document series, should never be displayed to a user without being clearly marked as untrustworthy. The flag "entered-in-error" is why this element is labeled as a modifier of other elements.
   * Some reporting work flows require that the original narrative of a final document never be altered; instead, only new narrative can be added. The composition resource has no explicit status for explicitly noting whether this business rule is in effect. This would be handled by an extension if required.
   */
  status: string;
  _status?: Element;
  /**
   * For clinical documents, this is usually the patient.
   */
  subject?: Reference;
  /**
   * For many compositions, the title is the same as the text or a display name of Composition.type (e.g. a "consultation" or "progress note"). Note that CDA does not make title mandatory, but there are no known cases where it is useful for title to be omitted, so it is mandatory here. Feedback on this requirement is welcome during the trial use period.
   */
  title: string;
  _title?: Element;
  /**
   * For Composition type, LOINC is ubiquitous and strongly endorsed by HL7. Most implementation guides will require a specific LOINC code, or use LOINC as an extensible binding.
   */
  type: CodeableConcept;
}
/**
 * Code Values for the Composition.status field
 */
export enum CompositionStatusCodes {
  PRELIMINARY = 'preliminary',
  FINAL = 'final',
  AMENDED = 'amended',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * A set of additional dependencies for this mapping to hold. This mapping is only applicable if the specified element can be resolved, and it has the specified value.
 */
export interface ConceptMapGroupElementTargetDependsOn extends BackboneElement {
  /**
   * The display is ignored when processing the map.
   */
  display?: string;
  _display?: Element;
  /**
   * A reference to an element that holds a coded value that corresponds to a code system property. The idea is that the information model carries an element somewhere that is labeled to correspond with a code system property.
   */
  property: string;
  _property?: Element;
  /**
   * An absolute URI that identifies the code system of the dependency code (if the source/dependency is a value set that crosses code systems).
   */
  system?: string;
  _system?: Element;
  /**
   * Identity (code or path) or the element/item/ValueSet/text that the map depends on / refers to.
   */
  value: string;
  _value?: Element;
}
/**
 * Ideally there would only be one map, with equal or equivalent mapping. But multiple maps are allowed for several narrower options, or to assert that other concepts are unmatched.
 */
export interface ConceptMapGroupElementTarget extends BackboneElement {
  /**
   * Identity (code or path) or the element/item that the map refers to.
   */
  code?: string;
  _code?: Element;
  /**
   * A description of status/issues in mapping that conveys additional information not represented in  the structured data.
   */
  comment?: string;
  _comment?: Element;
  /**
   * A set of additional dependencies for this mapping to hold. This mapping is only applicable if the specified element can be resolved, and it has the specified value.
   */
  dependsOn?: ConceptMapGroupElementTargetDependsOn[];
  /**
   * The display is ignored when processing the map.
   */
  display?: string;
  _display?: Element;
  /**
   * This element is labeled as a modifier because it may indicate that a target does not apply.
   */
  equivalence: string;
  _equivalence?: Element;
  /**
   * A set of additional outcomes from this mapping to other elements. To properly execute this mapping, the specified element must be mapped to some data element or source that is in context. The mapping may still be useful without a place for the additional data elements, but the equivalence cannot be relied on.
   */
  product?: ConceptMapGroupElementTargetDependsOn[];
}
/**
 * Code Values for the ConceptMap.group.element.target.equivalence field
 */
export enum ConceptMapGroupElementTargetEquivalenceCodes {
  RELATEDTO = 'relatedto',
  EQUIVALENT = 'equivalent',
  EQUAL = 'equal',
  WIDER = 'wider',
  SUBSUMES = 'subsumes',
  NARROWER = 'narrower',
  SPECIALIZES = 'specializes',
  INEXACT = 'inexact',
  UNMATCHED = 'unmatched',
  DISJOINT = 'disjoint',
}
/**
 * Generally, the ideal is that there would only be one mapping for each concept in the source value set, but a given concept may be mapped multiple times with different comments or dependencies.
 */
export interface ConceptMapGroupElement extends BackboneElement {
  /**
   * Identity (code or path) or the element/item being mapped.
   */
  code?: string;
  _code?: Element;
  /**
   * The display is ignored when processing the map.
   */
  display?: string;
  _display?: Element;
  /**
   * Ideally there would only be one map, with equal or equivalent mapping. But multiple maps are allowed for several narrower options, or to assert that other concepts are unmatched.
   */
  target?: ConceptMapGroupElementTarget[];
}
/**
 * This only applies if the source code has a system value that matches the system defined for the group.
 */
export interface ConceptMapGroupUnmapped extends BackboneElement {
  /**
   * The fixed code to use when the mode = 'fixed'  - all unmapped codes are mapped to a single fixed code.
   */
  code?: string;
  _code?: Element;
  /**
   * The display is ignored when processing the map.
   */
  display?: string;
  _display?: Element;
  /**
   * Defines which action to take if there is no match for the source concept in the target system designated for the group. One of 3 actions are possible: use the unmapped code (this is useful when doing a mapping between versions, and only a few codes have changed), use a fixed code (a default code), or alternatively, a reference to a different concept map can be provided (by canonical URL).
   */
  mode: string;
  _mode?: Element;
  /**
   * The canonical reference to an additional ConceptMap resource instance to use for mapping if this ConceptMap resource contains no matching mapping for the source concept.
   */
  url?: string;
  _url?: Element;
}
/**
 * Code Values for the ConceptMap.group.unmapped.mode field
 */
export enum ConceptMapGroupUnmappedModeCodes {
  PROVIDED = 'provided',
  FIXED = 'fixed',
  OTHER_MAP = 'other-map',
}
/**
 * A group of mappings that all have the same source and target system.
 */
export interface ConceptMapGroup extends BackboneElement {
  /**
   * Generally, the ideal is that there would only be one mapping for each concept in the source value set, but a given concept may be mapped multiple times with different comments or dependencies.
   */
  element: ConceptMapGroupElement[];
  /**
   * This is not needed if the source value set is specified and it contains concepts from only a single system.
   */
  source?: string;
  _source?: Element;
  /**
   * The specification of a particular code system version may be required for code systems which lack concept permanence.
   */
  sourceVersion?: string;
  _sourceVersion?: Element;
  /**
   * This is not needed if the target value set is specified and it contains concepts from only a single system. The group target may also be omitted if all of the target element equivalence values are 'unmatched'.
   */
  target?: string;
  _target?: Element;
  /**
   * The specification of a particular code system version may be required for code systems which lack concept permanence.
   */
  targetVersion?: string;
  _targetVersion?: Element;
  /**
   * This only applies if the source code has a system value that matches the system defined for the group.
   */
  unmapped?: ConceptMapGroupUnmapped;
}
/**
 * A statement of relationships from one set of concepts to one or more other concepts - either concepts in code systems, or data element/data element concepts, or classes in class models.
 */
export interface ConceptMap extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ConceptMap';
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * Frequently the copyright differs between the concept map and codes that are included. The copyright statement should clearly differentiate between these when required.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the concept map. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * The description is not intended to describe the semantics of the concept map. The description should capture its intended use, which is needed for ensuring integrity for its use in models across future changes.
   */
  description?: string;
  _description?: Element;
  /**
   * Allows filtering of concept maps that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * A group of mappings that all have the same source and target system.
   */
  group?: ConceptMapGroup[];
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this concept map outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier;
  /**
   * It may be possible for the concept map to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the concept map is the organization or individual primarily responsible for the maintenance and upkeep of the concept map. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the concept map. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the concept map. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this concept map.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Should be a version specific reference. URIs SHOULD be absolute. If there is no source or target value set, there is no specified context for the map (not recommended).  The source value set may select codes from either an explicit (standard or local) or implicit code system.
   */
  sourceUri?: string;
  _sourceUri?: Element;
  /**
   * Should be a version specific reference. URIs SHOULD be absolute. If there is no source or target value set, there is no specified context for the map (not recommended).  The source value set may select codes from either an explicit (standard or local) or implicit code system.
   */
  sourceCanonical?: string;
  _sourceCanonical?: Element;
  /**
   * Allows filtering of concept maps that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * Should be a version specific reference. URIs SHOULD be absolute. If there is no source or target value set, the is no specified context for the map.
   */
  targetUri?: string;
  _targetUri?: Element;
  /**
   * Should be a version specific reference. URIs SHOULD be absolute. If there is no source or target value set, the is no specified context for the map.
   */
  targetCanonical?: string;
  _targetCanonical?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different concept map instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the concept map with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the ConceptMap.status field
 */
export enum ConceptMapStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Clinical stage or grade of a condition. May include formal severity assessments.
 */
export interface ConditionStage extends BackboneElement {
  /**
   * Reference to a formal record of the evidence on which the staging assessment is based.
   */
  assessment?: Reference[];
  /**
   * A simple summary of the stage such as "Stage 3". The determination of the stage is disease-specific.
   */
  summary?: CodeableConcept;
  /**
   * The kind of staging, such as pathological or clinical staging.
   */
  type?: CodeableConcept;
}
/**
 * The evidence may be a simple list of coded symptoms/manifestations, or references to observations or formal assessments, or both.
 */
export interface ConditionEvidence extends BackboneElement {
  /**
   * A manifestation or symptom that led to the recording of this condition.
   */
  code?: CodeableConcept[];
  /**
   * Links to other relevant information, including pathology reports.
   */
  detail?: Reference[];
}
/**
 * A clinical condition, problem, diagnosis, or other event, situation, issue, or clinical concept that has risen to a level of concern.
 */
export interface Condition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Condition';
  /**
   * There is no explicit distinction between resolution and remission because in many cases the distinction is not clear. Age is generally used when the patient reports an age at which the Condition abated.  If there is no abatement element, it is unknown whether the condition has resolved or entered remission; applications and users should generally assume that the condition is still valid.  When abatementString exists, it implies the condition is abated.
   */
  abatementDateTime?: string;
  _abatementDateTime?: Element;
  /**
   * There is no explicit distinction between resolution and remission because in many cases the distinction is not clear. Age is generally used when the patient reports an age at which the Condition abated.  If there is no abatement element, it is unknown whether the condition has resolved or entered remission; applications and users should generally assume that the condition is still valid.  When abatementString exists, it implies the condition is abated.
   */
  abatementAge?: Age;
  /**
   * There is no explicit distinction between resolution and remission because in many cases the distinction is not clear. Age is generally used when the patient reports an age at which the Condition abated.  If there is no abatement element, it is unknown whether the condition has resolved or entered remission; applications and users should generally assume that the condition is still valid.  When abatementString exists, it implies the condition is abated.
   */
  abatementPeriod?: Period;
  /**
   * There is no explicit distinction between resolution and remission because in many cases the distinction is not clear. Age is generally used when the patient reports an age at which the Condition abated.  If there is no abatement element, it is unknown whether the condition has resolved or entered remission; applications and users should generally assume that the condition is still valid.  When abatementString exists, it implies the condition is abated.
   */
  abatementRange?: Range;
  /**
   * There is no explicit distinction between resolution and remission because in many cases the distinction is not clear. Age is generally used when the patient reports an age at which the Condition abated.  If there is no abatement element, it is unknown whether the condition has resolved or entered remission; applications and users should generally assume that the condition is still valid.  When abatementString exists, it implies the condition is abated.
   */
  abatementString?: string;
  _abatementString?: Element;
  /**
   * Individual who is making the condition statement.
   */
  asserter?: Reference;
  /**
   * Only used if not implicit in code found in Condition.code. If the use case requires attributes from the BodySite resource (e.g. to identify and track separately) then use the standard extension [bodySite](extension-bodysite.html).  May be a summary code, or a reference to a very precise definition of the location, or both.
   */
  bodySite?: CodeableConcept[];
  /**
   * The categorization is often highly contextual and may appear poorly differentiated or not very useful in other contexts.
   */
  category?: CodeableConcept[];
  /**
   * The data type is CodeableConcept because clinicalStatus has some clinical judgment involved, such that there might need to be more specificity than the required FHIR value set allows. For example, a SNOMED coding might allow for additional specificity.
   */
  clinicalStatus?: CodeableConcept;
  /**
   * Identification of the condition, problem or diagnosis.
   */
  code?: CodeableConcept;
  /**
   * This will typically be the encounter the event occurred within, but some activities may be initiated prior to or after the official completion of an encounter but still be tied to the context of the encounter. This record indicates the encounter this particular record is associated with.  In the case of a "new" diagnosis reflecting ongoing/revised information about the condition, this might be distinct from the first encounter in which the underlying condition was first "known".
   */
  encounter?: Reference;
  /**
   * The evidence may be a simple list of coded symptoms/manifestations, or references to observations or formal assessments, or both.
   */
  evidence?: ConditionEvidence[];
  /**
   * This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  identifier?: Identifier[];
  /**
   * Additional information about the Condition. This is a general notes/comments entry  for description of the Condition, its diagnosis and prognosis.
   */
  note?: Annotation[];
  /**
   * Age is generally used when the patient reports an age at which the Condition began to occur.
   */
  onsetDateTime?: string;
  _onsetDateTime?: Element;
  /**
   * Age is generally used when the patient reports an age at which the Condition began to occur.
   */
  onsetAge?: Age;
  /**
   * Age is generally used when the patient reports an age at which the Condition began to occur.
   */
  onsetPeriod?: Period;
  /**
   * Age is generally used when the patient reports an age at which the Condition began to occur.
   */
  onsetRange?: Range;
  /**
   * Age is generally used when the patient reports an age at which the Condition began to occur.
   */
  onsetString?: string;
  _onsetString?: Element;
  /**
   * The recordedDate represents when this particular Condition record was created in the system, which is often a system-generated date.
   */
  recordedDate?: string;
  _recordedDate?: Element;
  /**
   * Individual who recorded the record and takes responsibility for its content.
   */
  recorder?: Reference;
  /**
   * Coding of the severity with a terminology is preferred, where possible.
   */
  severity?: CodeableConcept;
  /**
   * Clinical stage or grade of a condition. May include formal severity assessments.
   */
  stage?: ConditionStage[];
  /**
   * Indicates the patient or group who the condition record is associated with.
   */
  subject: Reference;
  /**
   * verificationStatus is not required.  For example, when a patient has abdominal pain in the ED, there is not likely going to be a verification status.
   * The data type is CodeableConcept because verificationStatus has some clinical judgment involved, such that there might need to be more specificity than the required FHIR value set allows. For example, a SNOMED coding might allow for additional specificity.
   */
  verificationStatus?: CodeableConcept;
}
/**
 * The references to the policies that are included in this consent scope. Policies may be organizational, but are often defined jurisdictionally, or in law.
 */
export interface ConsentPolicy extends BackboneElement {
  /**
   * Entity or Organization having regulatory jurisdiction or accountability for  enforcing policies pertaining to Consent Directives.
   */
  authority?: string;
  _authority?: Element;
  /**
   * This element is for discoverability / documentation and does not modify or qualify the policy rules.
   */
  uri?: string;
  _uri?: Element;
}
/**
 * Whether a treatment instruction (e.g. artificial respiration yes or no) was verified with the patient, his/her family or another authorized person.
 */
export interface ConsentVerification extends BackboneElement {
  /**
   * Date verification was collected.
   */
  verificationDate?: string;
  _verificationDate?: Element;
  /**
   * Has the instruction been verified.
   */
  verified: boolean;
  _verified?: Element;
  /**
   * Who verified the instruction (Patient, Relative or other Authorized Person).
   */
  verifiedWith?: Reference;
}
/**
 * Who or what is controlled by this rule. Use group to identify a set of actors by some property they share (e.g. 'admitting officers').
 */
export interface ConsentProvisionActor extends BackboneElement {
  /**
   * The resource that identifies the actor. To identify actors by type, use group to identify a set of actors by some property they share (e.g. 'admitting officers').
   */
  reference: Reference;
  /**
   * How the individual is involved in the resources content that is described in the exception.
   */
  role: CodeableConcept;
}
/**
 * The resources controlled by this rule if specific resources are referenced.
 */
export interface ConsentProvisionData extends BackboneElement {
  /**
   * How the resource reference is interpreted when testing consent restrictions.
   */
  meaning: string;
  _meaning?: Element;
  /**
   * A reference to a specific resource that defines which resources are covered by this consent.
   */
  reference: Reference;
}
/**
 * Code Values for the Consent.provision.data.meaning field
 */
export enum ConsentProvisionDataMeaningCodes {
  INSTANCE = 'instance',
  RELATED = 'related',
  DEPENDENTS = 'dependents',
  AUTHOREDBY = 'authoredby',
}
/**
 * An exception to the base policy of this consent. An exception can be an addition or removal of access permissions.
 */
export interface ConsentProvision extends BackboneElement {
  /**
   * Note that this is the direct action (not the grounds for the action covered in the purpose element). At present, the only action in the understood and tested scope of this resource is 'read'.
   */
  action?: CodeableConcept[];
  /**
   * Who or what is controlled by this rule. Use group to identify a set of actors by some property they share (e.g. 'admitting officers').
   */
  actor?: ConsentProvisionActor[];
  /**
   * Multiple types are or'ed together. The intention of the contentType element is that the codes refer to profiles or document types defined in a standard or an implementation guide somewhere.
   */
  class?: Coding[];
  /**
   * Typical use of this is a Document code with class = CDA.
   */
  code?: CodeableConcept[];
  /**
   * The resources controlled by this rule if specific resources are referenced.
   */
  data?: ConsentProvisionData[];
  /**
   * This has a different sense to the Consent.period - that is when the consent agreement holds. This is the time period of the data that is controlled by the agreement.
   */
  dataPeriod?: Period;
  /**
   * The timeframe in this rule is valid.
   */
  period?: Period;
  /**
   * Rules which provide exceptions to the base rule or subrules.
   */
  provision?: ConsentProvision[];
  /**
   * When the purpose of use tag is on the data, access request purpose of use shall not conflict.
   */
  purpose?: Coding[];
  /**
   * If the consent specifies a security label of "R" then it applies to all resources that are labeled "R" or lower. E.g. for Confidentiality, it's a high water mark. For other kinds of security labels, subsumption logic applies. When the purpose of use tag is on the data, access request purpose of use shall not conflict.
   */
  securityLabel?: Coding[];
  /**
   * Action  to take - permit or deny - when the rule conditions are met.  Not permitted in root rule, required in all nested rules.
   */
  type?: string;
  _type?: Element;
}
/**
 * Code Values for the Consent.provision.type field
 */
export enum ConsentProvisionTypeCodes {
  DENY = 'deny',
  PERMIT = 'permit',
}
/**
 * A record of a healthcare consumer’s  choices, which permits or denies identified recipient(s) or recipient role(s) to perform one or more actions within a given policy context, for specific purposes and periods of time.
 */
export interface Consent extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Consent';
  /**
   * A classification of the type of consents found in the statement. This element supports indexing and retrieval of consent statements.
   */
  category: CodeableConcept[];
  /**
   * This is not the time of the original consent, but the time that this statement was made or derived.
   */
  dateTime?: string;
  _dateTime?: Element;
  /**
   * This identifier identifies this copy of the consent. Where this identifier is also used elsewhere as the identifier for a consent record (e.g. a CDA consent document) then the consent details are expected to be the same.
   */
  identifier?: Identifier[];
  /**
   * The organization that manages the consent, and the framework within which it is executed.
   */
  organization?: Reference[];
  /**
   * Commonly, the patient the consent pertains to is the author, but for young and old people, it may be some other person.
   */
  patient?: Reference;
  /**
   * Commonly, the patient the consent pertains to is the consentor, but particularly for young and old people, it may be some other person - e.g. a legal guardian.
   */
  performer?: Reference[];
  /**
   * The references to the policies that are included in this consent scope. Policies may be organizational, but are often defined jurisdictionally, or in law.
   */
  policy?: ConsentPolicy[];
  /**
   * If the policyRule is absent, computable consent would need to be constructed from the elements of the Consent resource.
   */
  policyRule?: CodeableConcept;
  /**
   * An exception to the base policy of this consent. An exception can be an addition or removal of access permissions.
   */
  provision?: ConsentProvision;
  /**
   * A selector of the type of consent being presented: ADR, Privacy, Treatment, Research.  This list is now extensible.
   */
  scope: CodeableConcept;
  /**
   * The source can be contained inline (Attachment), referenced directly (Consent), referenced in a consent repository (DocumentReference), or simply by an identifier (Identifier), e.g. a CDA document id.
   */
  sourceAttachment?: Attachment;
  /**
   * The source can be contained inline (Attachment), referenced directly (Consent), referenced in a consent repository (DocumentReference), or simply by an identifier (Identifier), e.g. a CDA document id.
   */
  sourceReference?: Reference;
  /**
   * This element is labeled as a modifier because the status contains the codes rejected and entered-in-error that mark the Consent as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * Whether a treatment instruction (e.g. artificial respiration yes or no) was verified with the patient, his/her family or another authorized person.
   */
  verification?: ConsentVerification[];
}
/**
 * Code Values for the Consent.status field
 */
export enum ConsentStatusCodes {
  DRAFT = 'draft',
  PROPOSED = 'proposed',
  ACTIVE = 'active',
  REJECTED = 'rejected',
  INACTIVE = 'inactive',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Precusory content developed with a focus and intent of supporting the formation a Contract instance, which may be associated with and transformable into a Contract.
 */
export interface ContractContentDefinition extends BackboneElement {
  /**
   * A copyright statement relating to Contract precursor content. Copyright statements are generally legal restrictions on the use and publishing of the Contract precursor content.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * The date (and optionally time) when the contract was published. The date must change when the business version changes and it must change if the status code changes. In addition, it should change when the substantive content of the contract changes.
   */
  publicationDate?: string;
  _publicationDate?: Element;
  /**
   * amended | appended | cancelled | disputed | entered-in-error | executable | executed | negotiable | offered | policy | rejected | renewed | revoked | resolved | terminated.
   */
  publicationStatus: string;
  _publicationStatus?: Element;
  /**
   * The  individual or organization that published the Contract precursor content.
   */
  publisher?: Reference;
  /**
   * Detailed Precusory content type.
   */
  subType?: CodeableConcept;
  /**
   * Precusory content structure and use, i.e., a boilerplate, template, application for a contract such as an insurance policy or benefits under a program, e.g., workers compensation.
   */
  type: CodeableConcept;
}
/**
 * Code Values for the Contract.contentDefinition.publicationStatus field
 */
export enum ContractContentDefinitionPublicationStatusCodes {
  AMENDED = 'amended',
  APPENDED = 'appended',
  CANCELLED = 'cancelled',
  DISPUTED = 'disputed',
  ENTERED_IN_ERROR = 'entered-in-error',
  EXECUTABLE = 'executable',
  EXECUTED = 'executed',
  NEGOTIABLE = 'negotiable',
  OFFERED = 'offered',
  POLICY = 'policy',
  REJECTED = 'rejected',
  RENEWED = 'renewed',
  REVOKED = 'revoked',
  RESOLVED = 'resolved',
  TERMINATED = 'terminated',
}
/**
 * Security labels that protect the handling of information about the term and its elements, which may be specifically identified..
 */
export interface ContractTermSecurityLabel extends BackboneElement {
  /**
   * Security label privacy tag that species the applicable privacy and security policies governing this term and/or term elements.
   */
  category?: Coding[];
  /**
   * Security label privacy tag that species the level of confidentiality protection required for this term and/or term elements.
   */
  classification: Coding;
  /**
   * Security label privacy tag that species the manner in which term and/or term elements are to be protected.
   */
  control?: Coding[];
  /**
   * Number used to link this term or term element to the applicable Security Label.
   */
  number?: number[];
}
/**
 * Offer Recipient.
 */
export interface ContractTermOfferParty extends BackboneElement {
  /**
   * Participant in the offer.
   */
  reference: Reference[];
  /**
   * How the party participates in the offer.
   */
  role: CodeableConcept;
}
/**
 * Response to offer text.
 */
export interface ContractTermOfferAnswer extends BackboneElement {
  /**
   * Response to an offer clause or question text,  which enables selection of values to be agreed to, e.g., the period of participation, the date of occupancy of a rental, warrently duration, or whether biospecimen may be used for further research.
   */
  valueBoolean: boolean;
  _valueBoolean?: Element;
  /**
   * Response to an offer clause or question text,  which enables selection of values to be agreed to, e.g., the period of participation, the date of occupancy of a rental, warrently duration, or whether biospecimen may be used for further research.
   */
  valueDecimal: number;
  /**
   * Response to an offer clause or question text,  which enables selection of values to be agreed to, e.g., the period of participation, the date of occupancy of a rental, warrently duration, or whether biospecimen may be used for further research.
   */
  valueInteger: number;
  /**
   * Response to an offer clause or question text,  which enables selection of values to be agreed to, e.g., the period of participation, the date of occupancy of a rental, warrently duration, or whether biospecimen may be used for further research.
   */
  valueDate: string;
  _valueDate?: Element;
  /**
   * Response to an offer clause or question text,  which enables selection of values to be agreed to, e.g., the period of participation, the date of occupancy of a rental, warrently duration, or whether biospecimen may be used for further research.
   */
  valueDateTime: string;
  _valueDateTime?: Element;
  /**
   * Response to an offer clause or question text,  which enables selection of values to be agreed to, e.g., the period of participation, the date of occupancy of a rental, warrently duration, or whether biospecimen may be used for further research.
   */
  valueTime: string;
  _valueTime?: Element;
  /**
   * Response to an offer clause or question text,  which enables selection of values to be agreed to, e.g., the period of participation, the date of occupancy of a rental, warrently duration, or whether biospecimen may be used for further research.
   */
  valueString: string;
  _valueString?: Element;
  /**
   * Response to an offer clause or question text,  which enables selection of values to be agreed to, e.g., the period of participation, the date of occupancy of a rental, warrently duration, or whether biospecimen may be used for further research.
   */
  valueUri: string;
  _valueUri?: Element;
  /**
   * Response to an offer clause or question text,  which enables selection of values to be agreed to, e.g., the period of participation, the date of occupancy of a rental, warrently duration, or whether biospecimen may be used for further research.
   */
  valueAttachment: Attachment;
  /**
   * Response to an offer clause or question text,  which enables selection of values to be agreed to, e.g., the period of participation, the date of occupancy of a rental, warrently duration, or whether biospecimen may be used for further research.
   */
  valueCoding: Coding;
  /**
   * Response to an offer clause or question text,  which enables selection of values to be agreed to, e.g., the period of participation, the date of occupancy of a rental, warrently duration, or whether biospecimen may be used for further research.
   */
  valueQuantity: Quantity;
  /**
   * Response to an offer clause or question text,  which enables selection of values to be agreed to, e.g., the period of participation, the date of occupancy of a rental, warrently duration, or whether biospecimen may be used for further research.
   */
  valueReference: Reference;
}
/**
 * The matter of concern in the context of this provision of the agrement.
 */
export interface ContractTermOffer extends BackboneElement {
  /**
   * Response to offer text.
   */
  answer?: ContractTermOfferAnswer[];
  /**
   * Type of choice made by accepting party with respect to an offer made by an offeror/ grantee.
   */
  decision?: CodeableConcept;
  /**
   * How the decision about a Contract was conveyed.
   */
  decisionMode?: CodeableConcept[];
  /**
   * Unique identifier for this particular Contract Provision.
   */
  identifier?: Identifier[];
  /**
   * The id of the clause or question text of the offer in the referenced questionnaire/response.
   */
  linkId?: string[];
  _linkId?: Element;
  /**
   * Offer Recipient.
   */
  party?: ContractTermOfferParty[];
  /**
   * Security labels that protects the offer.
   */
  securityLabelNumber?: number[];
  /**
   * Human readable form of this Contract Offer.
   */
  text?: string;
  _text?: Element;
  /**
   * The Contract.topic may be an application for or offer of a policy or service (e.g., uri to a consent directive form or a health insurance policy), which becomes the Contract once accepted by both the grantor and grantee.
   * The Contract Resource may function simply as the computable representation of the executed contract, which may be the attached to the Contract Resource as the “binding” or as the “friendly” electronic form.  For example, a Contract Resource may be automatically populated with the values expressed in a related QuestionnaireResponse.
   * However, the Contract Resource may be considered the legally binding contract if it is the only “executed” form of this contract, and includes the signatures as *The Contract Resource may function as the computable representation of an application or offer in a pre-executed Contract if the grantor has not entered any values.  In this case, it is populated with values in a “legal” form of the application or offer or by the values in an associated Questionnaire.  If the grantor has filled in the legal form or the associated Questionnaire Response, then these values are used to populate a pre-executed Contract Resource.
   * If the Contract.topic is considered an application or offer, then the policy is often required to be attached as the “legal” basis for the application to ensure “informed consent” to the contract, and that any discrepancy between the application and the policy are interpreted against the policy.  Implementers should check organizational and jurisdictional policies to determine the relationship among multiple representations of a contract pre- and post-execution.
   */
  topic?: Reference;
  /**
   * Type of Contract Provision such as specific requirements, purposes for actions, obligations, prohibitions, e.g. life time maximum benefit.
   */
  type?: CodeableConcept;
}
/**
 * Circumstance of the asset.
 */
export interface ContractTermAssetContext extends BackboneElement {
  /**
   * Coded representation of the context generally or of the Referenced entity, such as the asset holder type or location.
   */
  code?: CodeableConcept[];
  /**
   * Asset context reference may include the creator, custodian, or owning Person or Organization (e.g., bank, repository),  location held, e.g., building,  jurisdiction.
   */
  reference?: Reference;
  /**
   * Context description.
   */
  text?: string;
  _text?: Element;
}
/**
 * Contract Valued Item List.
 */
export interface ContractTermAssetValuedItem extends BackboneElement {
  /**
   * Indicates the time during which this Contract ValuedItem information is effective.
   */
  effectiveTime?: string;
  _effectiveTime?: Element;
  /**
   * Specific type of Contract Valued Item that may be priced.
   */
  entityCodeableConcept?: CodeableConcept;
  /**
   * Specific type of Contract Valued Item that may be priced.
   */
  entityReference?: Reference;
  /**
   * A real number that represents a multiplier used in determining the overall value of the Contract Valued Item delivered. The concept of a Factor allows for a discount or surcharge multiplier to be applied to a monetary amount.
   */
  factor?: number;
  /**
   * Identifies a Contract Valued Item instance.
   */
  identifier?: Identifier;
  /**
   * Id  of the clause or question text related to the context of this valuedItem in the referenced form or QuestionnaireResponse.
   */
  linkId?: string[];
  _linkId?: Element;
  /**
   * Expresses the product of the Contract Valued Item unitQuantity and the unitPriceAmt. For example, the formula: unit Quantity * unit Price (Cost per Point) * factor Number  * points = net Amount. Quantity, factor and points are assumed to be 1 if not supplied.
   */
  net?: Money;
  /**
   * Terms of valuation.
   */
  payment?: string;
  _payment?: Element;
  /**
   * When payment is due.
   */
  paymentDate?: string;
  _paymentDate?: Element;
  /**
   * An amount that expresses the weighting (based on difficulty, cost and/or resource intensiveness) associated with the Contract Valued Item delivered. The concept of Points allows for assignment of point values for a Contract Valued Item, such that a monetary amount can be assigned to each point.
   */
  points?: number;
  /**
   * Specifies the units by which the Contract Valued Item is measured or counted, and quantifies the countable or measurable Contract Valued Item instances.
   */
  quantity?: Quantity;
  /**
   * Who will receive payment.
   */
  recipient?: Reference;
  /**
   * Who will make payment.
   */
  responsible?: Reference;
  /**
   * A set of security labels that define which terms are controlled by this condition.
   */
  securityLabelNumber?: number[];
  /**
   * A Contract Valued Item unit valuation measure.
   */
  unitPrice?: Money;
}
/**
 * Contract Term Asset List.
 */
export interface ContractTermAsset extends BackboneElement {
  /**
   * Response to assets.
   */
  answer?: ContractTermOfferAnswer[];
  /**
   * Description of the quality and completeness of the asset that imay be a factor in its valuation.
   */
  condition?: string;
  _condition?: Element;
  /**
   * Circumstance of the asset.
   */
  context?: ContractTermAssetContext[];
  /**
   * Id [identifier??] of the clause or question text about the asset in the referenced form or QuestionnaireResponse.
   */
  linkId?: string[];
  _linkId?: Element;
  /**
   * Asset relevant contractual time period.
   */
  period?: Period[];
  /**
   * Type of Asset availability for use or ownership.
   */
  periodType?: CodeableConcept[];
  /**
   * Specifies the applicability of the term to an asset resource instance, and instances it refers to orinstances that refer to it, and/or are owned by the offeree.
   */
  relationship?: Coding;
  /**
   * Differentiates the kind of the asset .
   */
  scope?: CodeableConcept;
  /**
   * Security labels that protects the asset.
   */
  securityLabelNumber?: number[];
  /**
   * May be a subtype or part of an offered asset.
   */
  subtype?: CodeableConcept[];
  /**
   * Clause or question text (Prose Object) concerning the asset in a linked form, such as a QuestionnaireResponse used in the formation of the contract.
   */
  text?: string;
  _text?: Element;
  /**
   * Target entity type about which the term may be concerned.
   */
  type?: CodeableConcept[];
  /**
   * Associated entities.
   */
  typeReference?: Reference[];
  /**
   * Time period of asset use.
   */
  usePeriod?: Period[];
  /**
   * Contract Valued Item List.
   */
  valuedItem?: ContractTermAssetValuedItem[];
}
/**
 * Entity of the action.
 */
export interface ContractTermActionSubject extends BackboneElement {
  /**
   * The entity the action is performed or not performed on or for.
   */
  reference: Reference[];
  /**
   * Role type of agent assigned roles in this Contract.
   */
  role?: CodeableConcept;
}
/**
 * Several agents may be associated (i.e. has some responsibility for an activity) with an activity and vice-versa.
 * For example, in cases of actions initiated by one user for other users, or in events that involve more than one user, hardware device, software, or system process. However, only one user may be the initiator/requestor for the event.
 */
export interface ContractTermAction extends BackboneElement {
  /**
   * Encounter or Episode with primary association to specified term activity.
   */
  context?: Reference;
  /**
   * Id [identifier??] of the clause or question text related to the requester of this action in the referenced form or QuestionnaireResponse.
   */
  contextLinkId?: string[];
  _contextLinkId?: Element;
  /**
   * True if the term prohibits the  action.
   */
  doNotPerform?: boolean;
  _doNotPerform?: Element;
  /**
   * Reason or purpose for the action stipulated by this Contract Provision.
   */
  intent: CodeableConcept;
  /**
   * Id [identifier??] of the clause or question text related to this action in the referenced form or QuestionnaireResponse.
   */
  linkId?: string[];
  _linkId?: Element;
  /**
   * Comments made about the term action made by the requester, performer, subject or other participants.
   */
  note?: Annotation[];
  /**
   * When action happens.
   */
  occurrenceDateTime?: string;
  _occurrenceDateTime?: Element;
  /**
   * When action happens.
   */
  occurrencePeriod?: Period;
  /**
   * When action happens.
   */
  occurrenceTiming?: Timing;
  /**
   * Indicates who or what is being asked to perform (or not perform) the ction.
   */
  performer?: Reference;
  /**
   * Id [identifier??] of the clause or question text related to the reason type or reference of this  action in the referenced form or QuestionnaireResponse.
   */
  performerLinkId?: string[];
  _performerLinkId?: Element;
  /**
   * The type of role or competency of an individual desired or required to perform or not perform the action.
   */
  performerRole?: CodeableConcept;
  /**
   * The type of individual that is desired or required to perform or not perform the action.
   */
  performerType?: CodeableConcept[];
  /**
   * Describes why the action is to be performed or not performed in textual form.
   */
  reason?: string[];
  _reason?: Element;
  /**
   * Rationale for the action to be performed or not performed. Describes why the action is permitted or prohibited.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Id [identifier??] of the clause or question text related to the reason type or reference of this  action in the referenced form or QuestionnaireResponse.
   */
  reasonLinkId?: string[];
  _reasonLinkId?: Element;
  /**
   * Indicates another resource whose existence justifies permitting or not permitting this action.
   */
  reasonReference?: Reference[];
  /**
   * Who or what initiated the action and has responsibility for its activation.
   */
  requester?: Reference[];
  /**
   * Id [identifier??] of the clause or question text related to the requester of this action in the referenced form or QuestionnaireResponse.
   */
  requesterLinkId?: string[];
  _requesterLinkId?: Element;
  /**
   * Security labels that protects the action.
   */
  securityLabelNumber?: number[];
  /**
   * Current state of the term action.
   */
  status: CodeableConcept;
  /**
   * Entity of the action.
   */
  subject?: ContractTermActionSubject[];
  /**
   * Activity or service obligation to be done or not done, performed or not performed, effectuated or not by this Contract term.
   */
  type: CodeableConcept;
}
/**
 * One or more Contract Provisions, which may be related and conveyed as a group, and may contain nested groups.
 */
export interface ContractTerm extends BackboneElement {
  /**
   * Several agents may be associated (i.e. has some responsibility for an activity) with an activity and vice-versa.
   * For example, in cases of actions initiated by one user for other users, or in events that involve more than one user, hardware device, software, or system process. However, only one user may be the initiator/requestor for the event.
   */
  action?: ContractTermAction[];
  /**
   * Relevant time or time-period when this Contract Provision is applicable.
   */
  applies?: Period;
  /**
   * Contract Term Asset List.
   */
  asset?: ContractTermAsset[];
  /**
   * Nested group of Contract Provisions.
   */
  group?: ContractTerm[];
  /**
   * Unique identifier for this particular Contract Provision.
   */
  identifier?: Identifier;
  /**
   * When this Contract Provision was issued.
   */
  issued?: string;
  _issued?: Element;
  /**
   * The matter of concern in the context of this provision of the agrement.
   */
  offer: ContractTermOffer;
  /**
   * Security labels that protect the handling of information about the term and its elements, which may be specifically identified..
   */
  securityLabel?: ContractTermSecurityLabel[];
  /**
   * A specialized legal clause or condition based on overarching contract type.
   */
  subType?: CodeableConcept;
  /**
   * Statement of a provision in a policy or a contract.
   */
  text?: string;
  _text?: Element;
  /**
   * The entity that the term applies to.
   */
  topicCodeableConcept?: CodeableConcept;
  /**
   * The entity that the term applies to.
   */
  topicReference?: Reference;
  /**
   * A legal clause or condition contained within a contract that requires one or both parties to perform a particular requirement by some specified time or prevents one or both parties from performing a particular requirement by some specified time.
   */
  type?: CodeableConcept;
}
/**
 * Signers who are principal parties to the contract are bound by the Contract.activity related to the Contract.topic, and the Contract.term(s), which either extend or restrict the overall action on the topic by, for example, stipulating specific policies or obligations constraining actions, action reason, or agents with respect to some or all of the topic.
 * For example, specifying how policies or obligations shall constrain actions and action reasons permitted or denied on all or a subset of the Contract.topic (e.g., all or a portion of property being transferred by the contract), agents (e.g., who can resell, assign interests, or alter the property being transferred by the contract), actions, and action reasons; or with respect to Contract.terms, stipulating, extending, or limiting the Contract.period of applicability or valuation of items under consideration.
 */
export interface ContractSigner extends BackboneElement {
  /**
   * Party which is a signator to this Contract.
   */
  party: Reference;
  /**
   * Legally binding Contract DSIG signature contents in Base64.
   */
  signature: Signature[];
  /**
   * Role of this Contract signer, e.g. notary, grantee.
   */
  type: Coding;
}
/**
 * The "patient friendly language" versionof the Contract in whole or in parts. "Patient friendly language" means the representation of the Contract and Contract Provisions in a manner that is readily accessible and understandable by a layperson in accordance with best practices for communication styles that ensure that those agreeing to or signing the Contract understand the roles, actions, obligations, responsibilities, and implication of the agreement.
 */
export interface ContractFriendly extends BackboneElement {
  /**
   * Human readable rendering of this Contract in a format and representation intended to enhance comprehension and ensure understandability.
   */
  contentAttachment: Attachment;
  /**
   * Human readable rendering of this Contract in a format and representation intended to enhance comprehension and ensure understandability.
   */
  contentReference: Reference;
}
/**
 * List of Legal expressions or representations of this Contract.
 */
export interface ContractLegal extends BackboneElement {
  /**
   * Contract legal text in human renderable form.
   */
  contentAttachment: Attachment;
  /**
   * Contract legal text in human renderable form.
   */
  contentReference: Reference;
}
/**
 * List of Computable Policy Rule Language Representations of this Contract.
 */
export interface ContractRule extends BackboneElement {
  /**
   * Computable Contract conveyed using a policy rule language (e.g. XACML, DKAL, SecPal).
   */
  contentAttachment: Attachment;
  /**
   * Computable Contract conveyed using a policy rule language (e.g. XACML, DKAL, SecPal).
   */
  contentReference: Reference;
}
/**
 * Legally enforceable, formally recorded unilateral or bilateral directive i.e., a policy or agreement.
 */
export interface Contract extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Contract';
  /**
   * Alternative representation of the title for this Contract definition, derivative, or instance in any legal state., e.g., a domain specific contract number related to legislation.
   */
  alias?: string[];
  _alias?: Element;
  /**
   * Relevant time or time-period when this Contract is applicable.
   */
  applies?: Period;
  /**
   * The individual or organization that authored the Contract definition, derivative, or instance in any legal state.
   */
  author?: Reference;
  /**
   * A formally or informally recognized grouping of people, principals, organizations, or jurisdictions formed for the purpose of achieving some form of collective action such as the promulgation, administration and enforcement of contracts and policies.
   */
  authority?: Reference[];
  /**
   * Precusory content developed with a focus and intent of supporting the formation a Contract instance, which may be associated with and transformable into a Contract.
   */
  contentDefinition?: ContractContentDefinition;
  /**
   * The minimal content derived from the basal information source at a specific stage in its lifecycle.
   */
  contentDerivative?: CodeableConcept;
  /**
   * Recognized governance framework or system operating with a circumscribed scope in accordance with specified principles, policies, processes or procedures for managing rights, actions, or behaviors of parties or principals relative to resources.
   */
  domain?: Reference[];
  /**
   * Event resulting in discontinuation or termination of this Contract instance by one or more parties to the contract.
   */
  expirationType?: CodeableConcept;
  /**
   * The "patient friendly language" versionof the Contract in whole or in parts. "Patient friendly language" means the representation of the Contract and Contract Provisions in a manner that is readily accessible and understandable by a layperson in accordance with best practices for communication styles that ensure that those agreeing to or signing the Contract understand the roles, actions, obligations, responsibilities, and implication of the agreement.
   */
  friendly?: ContractFriendly[];
  /**
   * Unique identifier for this Contract or a derivative that references a Source Contract.
   */
  identifier?: Identifier[];
  /**
   * The URL pointing to a FHIR-defined Contract Definition that is adhered to in whole or part by this Contract.
   */
  instantiatesCanonical?: Reference;
  /**
   * The URL pointing to an externally maintained definition that is adhered to in whole or in part by this Contract.
   */
  instantiatesUri?: string;
  _instantiatesUri?: Element;
  /**
   * When this  Contract was issued.
   */
  issued?: string;
  _issued?: Element;
  /**
   * List of Legal expressions or representations of this Contract.
   */
  legal?: ContractLegal[];
  /**
   * Legally binding Contract: This is the signed and legally recognized representation of the Contract, which is considered the "source of truth" and which would be the basis for legal action related to enforcement of this Contract.
   */
  legallyBindingAttachment?: Attachment;
  /**
   * Legally binding Contract: This is the signed and legally recognized representation of the Contract, which is considered the "source of truth" and which would be the basis for legal action related to enforcement of this Contract.
   */
  legallyBindingReference?: Reference;
  /**
   * Legal states of the formation of a legal instrument, which is a formally executed written document that can be formally attributed to its author, records and formally expresses a legally enforceable act, process, or contractual duty, obligation, or right, and therefore evidences that act, process, or agreement.
   */
  legalState?: CodeableConcept;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * Links to Provenance records for past versions of this Contract definition, derivative, or instance, which identify key state transitions or updates that are likely to be relevant to a user looking at the current version of the Contract.  The Provence.entity indicates the target that was changed in the update. http://build.fhir.org/provenance-definitions.html#Provenance.entity.
   */
  relevantHistory?: Reference[];
  /**
   * List of Computable Policy Rule Language Representations of this Contract.
   */
  rule?: ContractRule[];
  /**
   * A selector of legal concerns for this Contract definition, derivative, or instance in any legal state.
   */
  scope?: CodeableConcept;
  /**
   * Signers who are principal parties to the contract are bound by the Contract.activity related to the Contract.topic, and the Contract.term(s), which either extend or restrict the overall action on the topic by, for example, stipulating specific policies or obligations constraining actions, action reason, or agents with respect to some or all of the topic.
   * For example, specifying how policies or obligations shall constrain actions and action reasons permitted or denied on all or a subset of the Contract.topic (e.g., all or a portion of property being transferred by the contract), agents (e.g., who can resell, assign interests, or alter the property being transferred by the contract), actions, and action reasons; or with respect to Contract.terms, stipulating, extending, or limiting the Contract.period of applicability or valuation of items under consideration.
   */
  signer?: ContractSigner[];
  /**
   * Sites in which the contract is complied with,  exercised, or in force.
   */
  site?: Reference[];
  /**
   * This element is labeled as a modifier because the status contains codes that mark the contract as not currently valid or active.
   */
  status?: string;
  _status?: Element;
  /**
   * The Contract.subject is an entity that has some role with respect to the Contract.topic and Contract.topic.term, which is of focal interest to the parties to the contract and likely impacted in a significant way by the Contract.action/Contract.action.reason and the Contract.term.action/Contract.action.reason.
   * In many cases, the Contract.subject is a Contract.signer if the subject is an adult; has a legal interest in the contract; and incompetent to participate in the contract agreement.
   */
  subject?: Reference[];
  /**
   * An explanatory or alternate user-friendly title for this Contract definition, derivative, or instance in any legal state.t giving additional information about its content.
   */
  subtitle?: string;
  _subtitle?: Element;
  /**
   * Sub-category for the Contract that distinguishes the kinds of systems that would be interested in the Contract within the context of the Contract's scope.
   */
  subType?: CodeableConcept[];
  /**
   * Information that may be needed by/relevant to the performer in their execution of this term action.
   */
  supportingInfo?: Reference[];
  /**
   * One or more Contract Provisions, which may be related and conveyed as a group, and may contain nested groups.
   */
  term?: ContractTerm[];
  /**
   * A short, descriptive, user-friendly title for this Contract definition, derivative, or instance in any legal state.t giving additional information about its content.
   */
  title?: string;
  _title?: Element;
  /**
   * Narrows the range of legal concerns to focus on the achievement of specific contractual objectives.
   */
  topicCodeableConcept?: CodeableConcept;
  /**
   * Narrows the range of legal concerns to focus on the achievement of specific contractual objectives.
   */
  topicReference?: Reference;
  /**
   * A high-level category for the legal instrument, whether constructed as a Contract definition, derivative, or instance in any legal state.  Provides additional information about its content within the context of the Contract's scope to distinguish the kinds of systems that would be interested in the contract.
   */
  type?: CodeableConcept;
  /**
   * Used in a domain that uses a supplied contract repository.
   */
  url?: string;
  _url?: Element;
  /**
   * Note -  This is a business versionId, not a resource version id (see discussion http://build.fhir.org/resource.html#versions)
   * Comments - There may be different contract instances that have the same identifier but different versions. The version can be appended to the url in a reference to allow a reference to a particular business version of the plan definition with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the Contract.status field
 */
export enum ContractStatusCodes {
  AMENDED = 'amended',
  APPENDED = 'appended',
  CANCELLED = 'cancelled',
  DISPUTED = 'disputed',
  ENTERED_IN_ERROR = 'entered-in-error',
  EXECUTABLE = 'executable',
  EXECUTED = 'executed',
  NEGOTIABLE = 'negotiable',
  OFFERED = 'offered',
  POLICY = 'policy',
  REJECTED = 'rejected',
  RENEWED = 'renewed',
  REVOKED = 'revoked',
  RESOLVED = 'resolved',
  TERMINATED = 'terminated',
}
/**
 * For example may be used to identify a class of coverage or employer group, Policy, Plan.
 */
export interface CoverageClass extends BackboneElement {
  /**
   * A short description for the class.
   */
  name?: string;
  _name?: Element;
  /**
   * The type of classification for which an insurer-specific class label or number and optional name is provided, for example may be used to identify a class of coverage or employer group, Policy, Plan.
   */
  type: CodeableConcept;
  /**
   * For example, the Group or Plan number.
   */
  value: string;
  _value?: Element;
}
/**
 * A suite of codes indicating exceptions or reductions to patient costs and their effective periods.
 */
export interface CoverageCostToBeneficiaryException extends BackboneElement {
  /**
   * The timeframe during when the exception is in force.
   */
  period?: Period;
  /**
   * The code for the specific exception.
   */
  type: CodeableConcept;
}
/**
 * For example by knowing the patient visit co-pay, the provider can collect the amount prior to undertaking treatment.
 */
export interface CoverageCostToBeneficiary extends BackboneElement {
  /**
   * A suite of codes indicating exceptions or reductions to patient costs and their effective periods.
   */
  exception?: CoverageCostToBeneficiaryException[];
  /**
   * For example visit, specialist visits, emergency, inpatient care, etc.
   */
  type?: CodeableConcept;
  /**
   * Amount may be expressed as a percentage of the service/product cost or a fixed amount of currency.
   */
  valueQuantity: Quantity;
  /**
   * Amount may be expressed as a percentage of the service/product cost or a fixed amount of currency.
   */
  valueMoney: Money;
}
/**
 * Financial instrument which may be used to reimburse or pay for health care products and services. Includes both insurance and self-payment.
 */
export interface Coverage extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Coverage';
  /**
   * The party who benefits from the insurance coverage; the patient when products and/or services are provided.
   */
  beneficiary: Reference;
  /**
   * For example may be used to identify a class of coverage or employer group, Policy, Plan.
   */
  class?: CoverageClass[];
  /**
   * The policy(s) which constitute this insurance coverage.
   */
  contract?: Reference[];
  /**
   * For example by knowing the patient visit co-pay, the provider can collect the amount prior to undertaking treatment.
   */
  costToBeneficiary?: CoverageCostToBeneficiary[];
  /**
   * Periodically the member number is constructed from the subscriberId and the dependant number.
   */
  dependent?: string;
  _dependent?: Element;
  /**
   * The main (and possibly only) identifier for the coverage - often referred to as a Member Id, Certificate number, Personal Health Number or Case ID. May be constructed as the concatenation of the Coverage.SubscriberID and the Coverage.dependant.
   */
  identifier?: Identifier[];
  /**
   * The insurer-specific identifier for the insurer-defined network of providers to which the beneficiary may seek treatment which will be covered at the 'in-network' rate, otherwise 'out of network' terms and conditions apply.
   */
  network?: string;
  _network?: Element;
  /**
   * The order of applicability of this coverage relative to other coverages which are currently in force. Note, there may be gaps in the numbering and this does not imply primary, secondary etc. as the specific positioning of coverages depends upon the episode of care.
   */
  order?: number;
  /**
   * May provide multiple identifiers such as insurance company identifier or business identifier (BIN number).
   * For selfpay it may provide multiple paying persons and/or organizations.
   */
  payor: Reference[];
  /**
   * Time period during which the coverage is in force. A missing start date indicates the start date isn't known, a missing end date means the coverage is continuing to be in force.
   */
  period?: Period;
  /**
   * For example: may be an individual, corporation or the subscriber's employer.
   */
  policyHolder?: Reference;
  /**
   * Typically, an individual uses policies which are theirs (relationship='self') before policies owned by others.
   */
  relationship?: CodeableConcept;
  /**
   * This element is labeled as a modifier because the status contains the code entered-in-error that marks the coverage as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * Typically, automotive and worker's compensation policies would be flagged with 'subrogation=true' to enable healthcare payors to collect against accident claims.
   */
  subrogation?: boolean;
  _subrogation?: Element;
  /**
   * May be self or a parent in the case of dependants.
   */
  subscriber?: Reference;
  /**
   * The insurer assigned ID for the Subscriber.
   */
  subscriberId?: string;
  _subscriberId?: Element;
  /**
   * The type of coverage: social program, medical plan, accident coverage (workers compensation, auto), group health or payment by an individual or organization.
   */
  type?: CodeableConcept;
}
/**
 * Code Values for the Coverage.status field
 */
export enum CoverageStatusCodes {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  DRAFT = 'draft',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Often there are multiple jurisdiction specific valuesets which are required.
 */
export interface CoverageEligibilityRequestSupportingInfo extends BackboneElement {
  /**
   * The supporting materials are applicable for all detail items, product/servce categories and specific billing codes.
   */
  appliesToAll?: boolean;
  _appliesToAll?: Element;
  /**
   * Could be used to provide references to other resources, document. For example could contain a PDF in an Attachment of the Police Report for an Accident.
   */
  information: Reference;
  /**
   * A number to uniquely identify supporting information entries.
   */
  sequence: number;
}
/**
 * All insurance coverages for the patient which may be applicable for reimbursement, of the products and services listed in the claim, are typically provided in the claim to allow insurers to confirm the ordering of the insurance coverages relative to local 'coordination of benefit' rules. One coverage (and only one) with 'focal=true' is to be used in the adjudication of this claim. Coverages appearing before the focal Coverage in the list, and where 'subrogation=false', should provide a reference to the ClaimResponse containing the adjudication results of the prior claim.
 */
export interface CoverageEligibilityRequestInsurance extends BackboneElement {
  /**
   * A business agreement number established between the provider and the insurer for special business processing purposes.
   */
  businessArrangement?: string;
  _businessArrangement?: Element;
  /**
   * Reference to the insurance card level information contained in the Coverage resource. The coverage issuing insurer will use these details to locate the patient's actual coverage within the insurer's information system.
   */
  coverage: Reference;
  /**
   * A patient may (will) have multiple insurance policies which provide reimburement for healthcare services and products. For example a person may also be covered by their spouse's policy and both appear in the list (and may be from the same insurer). This flag will be set to true for only one of the listed policies and that policy will be used for evaluating this request. Other requests would be created to request evaluation against the other listed policies.
   */
  focal?: boolean;
  _focal?: Element;
}
/**
 * Patient diagnosis for which care is sought.
 */
export interface CoverageEligibilityRequestItemDiagnosis extends BackboneElement {
  /**
   * The nature of illness or problem in a coded form or as a reference to an external defined Condition.
   */
  diagnosisCodeableConcept?: CodeableConcept;
  /**
   * The nature of illness or problem in a coded form or as a reference to an external defined Condition.
   */
  diagnosisReference?: Reference;
}
/**
 * Service categories or billable services for which benefit details and/or an authorization prior to service delivery may be required by the payor.
 */
export interface CoverageEligibilityRequestItem extends BackboneElement {
  /**
   * Examples include Medical Care, Periodontics, Renal Dialysis, Vision Coverage.
   */
  category?: CodeableConcept;
  /**
   * The plan/proposal/order describing the proposed service in detail.
   */
  detail?: Reference[];
  /**
   * Patient diagnosis for which care is sought.
   */
  diagnosis?: CoverageEligibilityRequestItemDiagnosis[];
  /**
   * Facility where the services will be provided.
   */
  facility?: Reference;
  /**
   * For example in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or out of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * Code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI).
   */
  productOrService?: CodeableConcept;
  /**
   * The practitioner who is responsible for the product or service to be rendered to the patient.
   */
  provider?: Reference;
  /**
   * The number of repetitions of a service or product.
   */
  quantity?: Quantity;
  /**
   * Exceptions, special conditions and supporting information applicable for this service or product line.
   */
  supportingInfoSequence?: number[];
  /**
   * The amount charged to the patient by the provider for a single unit.
   */
  unitPrice?: Money;
}
/**
 * The CoverageEligibilityRequest provides patient and insurance coverage information to an insurer for them to respond, in the form of an CoverageEligibilityResponse, with information regarding whether the stated coverage is valid and in-force and optionally to provide the insurance details of the policy.
 */
export interface CoverageEligibilityRequest extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'CoverageEligibilityRequest';
  /**
   * The date when this resource was created.
   */
  created: string;
  _created?: Element;
  /**
   * Person who created the request.
   */
  enterer?: Reference;
  /**
   * Facility where the services are intended to be provided.
   */
  facility?: Reference;
  /**
   * A unique identifier assigned to this coverage eligiblity request.
   */
  identifier?: Identifier[];
  /**
   * All insurance coverages for the patient which may be applicable for reimbursement, of the products and services listed in the claim, are typically provided in the claim to allow insurers to confirm the ordering of the insurance coverages relative to local 'coordination of benefit' rules. One coverage (and only one) with 'focal=true' is to be used in the adjudication of this claim. Coverages appearing before the focal Coverage in the list, and where 'subrogation=false', should provide a reference to the ClaimResponse containing the adjudication results of the prior claim.
   */
  insurance?: CoverageEligibilityRequestInsurance[];
  /**
   * The Insurer who issued the coverage in question and is the recipient of the request.
   */
  insurer: Reference;
  /**
   * Service categories or billable services for which benefit details and/or an authorization prior to service delivery may be required by the payor.
   */
  item?: CoverageEligibilityRequestItem[];
  /**
   * 1..1.
   */
  patient: Reference;
  /**
   * When the requestor expects the processor to complete processing.
   */
  priority?: CodeableConcept;
  /**
   * Typically this field would be 1..1 where this party is responsible for the eligibility request but not necessarily professionally responsible for the provision of the individual products and services listed below.
   */
  provider?: Reference;
  /**
   * Code to specify whether requesting: prior authorization requirements for some service categories or billing codes; benefits for coverages specified or discovered; discovery and return of coverages for the patient; and/or validation that the specified coverage is in-force at the date/period specified or 'now' if not specified.
   */
  purpose: string[];
  _purpose?: Element;
  /**
   * The date or dates when the enclosed suite of services were performed or completed.
   */
  servicedDate?: string;
  _servicedDate?: Element;
  /**
   * The date or dates when the enclosed suite of services were performed or completed.
   */
  servicedPeriod?: Period;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * Often there are multiple jurisdiction specific valuesets which are required.
   */
  supportingInfo?: CoverageEligibilityRequestSupportingInfo[];
}
/**
 * Code Values for the CoverageEligibilityRequest.purpose field
 */
export enum CoverageEligibilityRequestPurposeCodes {
  AUTH_REQUIREMENTS = 'auth-requirements',
  BENEFITS = 'benefits',
  DISCOVERY = 'discovery',
  VALIDATION = 'validation',
}
/**
 * Code Values for the CoverageEligibilityRequest.status field
 */
export enum CoverageEligibilityRequestStatusCodes {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  DRAFT = 'draft',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Benefits used to date.
 */
export interface CoverageEligibilityResponseInsuranceItemBenefit extends BackboneElement {
  /**
   * The quantity of the benefit which is permitted under the coverage.
   */
  allowedUnsignedInt?: number;
  /**
   * The quantity of the benefit which is permitted under the coverage.
   */
  allowedString?: string;
  _allowedString?: Element;
  /**
   * The quantity of the benefit which is permitted under the coverage.
   */
  allowedMoney?: Money;
  /**
   * For example: deductible, visits, benefit amount.
   */
  type: CodeableConcept;
  /**
   * The quantity of the benefit which have been consumed to date.
   */
  usedUnsignedInt?: number;
  /**
   * The quantity of the benefit which have been consumed to date.
   */
  usedString?: string;
  _usedString?: Element;
  /**
   * The quantity of the benefit which have been consumed to date.
   */
  usedMoney?: Money;
}
/**
 * Benefits and optionally current balances, and authorization details by category or service.
 */
export interface CoverageEligibilityResponseInsuranceItem extends BackboneElement {
  /**
   * A boolean flag indicating whether a preauthorization is required prior to actual service delivery.
   */
  authorizationRequired?: boolean;
  _authorizationRequired?: Element;
  /**
   * Codes or comments regarding information or actions associated with the preauthorization.
   */
  authorizationSupporting?: CodeableConcept[];
  /**
   * A web location for obtaining requirements or descriptive information regarding the preauthorization.
   */
  authorizationUrl?: string;
  _authorizationUrl?: Element;
  /**
   * Benefits used to date.
   */
  benefit?: CoverageEligibilityResponseInsuranceItemBenefit[];
  /**
   * Examples include Medical Care, Periodontics, Renal Dialysis, Vision Coverage.
   */
  category?: CodeableConcept;
  /**
   * For example 'DENT2 covers 100% of basic, 50% of major but excludes Ortho, Implants and Cosmetic services'.
   */
  description?: string;
  _description?: Element;
  /**
   * True if the indicated class of service is excluded from the plan, missing or False indicates the product or service is included in the coverage.
   */
  excluded?: boolean;
  _excluded?: Element;
  /**
   * For example in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or out of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * For example: MED01, or DENT2.
   */
  name?: string;
  _name?: Element;
  /**
   * Is a flag to indicate whether the benefits refer to in-network providers or out-of-network providers.
   */
  network?: CodeableConcept;
  /**
   * Code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI).
   */
  productOrService?: CodeableConcept;
  /**
   * The practitioner who is eligible for the provision of the product or service.
   */
  provider?: Reference;
  /**
   * The term or period of the values such as 'maximum lifetime benefit' or 'maximum annual visits'.
   */
  term?: CodeableConcept;
  /**
   * Indicates if the benefits apply to an individual or to the family.
   */
  unit?: CodeableConcept;
}
/**
 * All insurance coverages for the patient which may be applicable for reimbursement, of the products and services listed in the claim, are typically provided in the claim to allow insurers to confirm the ordering of the insurance coverages relative to local 'coordination of benefit' rules. One coverage (and only one) with 'focal=true' is to be used in the adjudication of this claim. Coverages appearing before the focal Coverage in the list, and where 'subrogation=false', should provide a reference to the ClaimResponse containing the adjudication results of the prior claim.
 */
export interface CoverageEligibilityResponseInsurance extends BackboneElement {
  /**
   * The term of the benefits documented in this response.
   */
  benefitPeriod?: Period;
  /**
   * Reference to the insurance card level information contained in the Coverage resource. The coverage issuing insurer will use these details to locate the patient's actual coverage within the insurer's information system.
   */
  coverage: Reference;
  /**
   * Flag indicating if the coverage provided is inforce currently if no service date(s) specified or for the whole duration of the service dates.
   */
  inforce?: boolean;
  _inforce?: Element;
  /**
   * Benefits and optionally current balances, and authorization details by category or service.
   */
  item?: CoverageEligibilityResponseInsuranceItem[];
}
/**
 * Errors encountered during the processing of the request.
 */
export interface CoverageEligibilityResponseError extends BackboneElement {
  /**
   * An error code,from a specified code system, which details why the eligibility check could not be performed.
   */
  code: CodeableConcept;
}
/**
 * This resource provides eligibility and plan details from the processing of an CoverageEligibilityRequest resource.
 */
export interface CoverageEligibilityResponse extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'CoverageEligibilityResponse';
  /**
   * The date this resource was created.
   */
  created: string;
  _created?: Element;
  /**
   * A human readable description of the status of the adjudication.
   */
  disposition?: string;
  _disposition?: Element;
  /**
   * Errors encountered during the processing of the request.
   */
  error?: CoverageEligibilityResponseError[];
  /**
   * May be needed to identify specific jurisdictional forms.
   */
  form?: CodeableConcept;
  /**
   * A unique identifier assigned to this coverage eligiblity request.
   */
  identifier?: Identifier[];
  /**
   * All insurance coverages for the patient which may be applicable for reimbursement, of the products and services listed in the claim, are typically provided in the claim to allow insurers to confirm the ordering of the insurance coverages relative to local 'coordination of benefit' rules. One coverage (and only one) with 'focal=true' is to be used in the adjudication of this claim. Coverages appearing before the focal Coverage in the list, and where 'subrogation=false', should provide a reference to the ClaimResponse containing the adjudication results of the prior claim.
   */
  insurance?: CoverageEligibilityResponseInsurance[];
  /**
   * The Insurer who issued the coverage in question and is the author of the response.
   */
  insurer: Reference;
  /**
   * The resource may be used to indicate that: the request has been held (queued) for processing; that it has been processed and errors found (error); that no errors were found and that some of the adjudication has been undertaken (partial) or that all of the adjudication has been undertaken (complete).
   */
  outcome: string;
  _outcome?: Element;
  /**
   * The party who is the beneficiary of the supplied coverage and for whom eligibility is sought.
   */
  patient: Reference;
  /**
   * A reference from the Insurer to which these services pertain to be used on further communication and as proof that the request occurred.
   */
  preAuthRef?: string;
  _preAuthRef?: Element;
  /**
   * Code to specify whether requesting: prior authorization requirements for some service categories or billing codes; benefits for coverages specified or discovered; discovery and return of coverages for the patient; and/or validation that the specified coverage is in-force at the date/period specified or 'now' if not specified.
   */
  purpose: string[];
  _purpose?: Element;
  /**
   * Reference to the original request resource.
   */
  request: Reference;
  /**
   * Typically this field would be 1..1 where this party is responsible for the claim but not necessarily professionally responsible for the provision of the individual products and services listed below.
   */
  requestor?: Reference;
  /**
   * The date or dates when the enclosed suite of services were performed or completed.
   */
  servicedDate?: string;
  _servicedDate?: Element;
  /**
   * The date or dates when the enclosed suite of services were performed or completed.
   */
  servicedPeriod?: Period;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
}
/**
 * Code Values for the CoverageEligibilityResponse.outcome field
 */
export enum CoverageEligibilityResponseOutcomeCodes {
  QUEUED = 'queued',
  COMPLETE = 'complete',
  ERROR = 'error',
  PARTIAL = 'partial',
}
/**
 * Code Values for the CoverageEligibilityResponse.purpose field
 */
export enum CoverageEligibilityResponsePurposeCodes {
  AUTH_REQUIREMENTS = 'auth-requirements',
  BENEFITS = 'benefits',
  DISCOVERY = 'discovery',
  VALIDATION = 'validation',
}
/**
 * Code Values for the CoverageEligibilityResponse.status field
 */
export enum CoverageEligibilityResponseStatusCodes {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  DRAFT = 'draft',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Supporting evidence or manifestations that provide the basis for identifying the detected issue such as a GuidanceResponse or MeasureReport.
 */
export interface DetectedIssueEvidence extends BackboneElement {
  /**
   * A manifestation that led to the recording of this detected issue.
   */
  code?: CodeableConcept[];
  /**
   * Links to resources that constitute evidence for the detected issue such as a GuidanceResponse or MeasureReport.
   */
  detail?: Reference[];
}
/**
 * Indicates an action that has been taken or is committed to reduce or eliminate the likelihood of the risk identified by the detected issue from manifesting.  Can also reflect an observation of known mitigating factors that may reduce/eliminate the need for any action.
 */
export interface DetectedIssueMitigation extends BackboneElement {
  /**
   * The "text" component can be used for detail or when no appropriate code exists.
   */
  action: CodeableConcept;
  /**
   * Identifies the practitioner who determined the mitigation and takes responsibility for the mitigation step occurring.
   */
  author?: Reference;
  /**
   * This might not be the same as when the mitigating step was actually taken.
   */
  date?: string;
  _date?: Element;
}
/**
 * Indicates an actual or potential clinical issue with or between one or more active or proposed clinical actions for a patient; e.g. Drug-drug interaction, Ineffective treatment frequency, Procedure-condition conflict, etc.
 */
export interface DetectedIssue extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'DetectedIssue';
  /**
   * Individual or device responsible for the issue being raised.  For example, a decision support application or a pharmacist conducting a medication review.
   */
  author?: Reference;
  /**
   * Identifies the general type of issue identified.
   */
  code?: CodeableConcept;
  /**
   * Should focus on information not covered elsewhere as discrete data - no need to duplicate the narrative.
   */
  detail?: string;
  _detail?: Element;
  /**
   * Supporting evidence or manifestations that provide the basis for identifying the detected issue such as a GuidanceResponse or MeasureReport.
   */
  evidence?: DetectedIssueEvidence[];
  /**
   * The date or period when the detected issue was initially identified.
   */
  identifiedDateTime?: string;
  _identifiedDateTime?: Element;
  /**
   * The date or period when the detected issue was initially identified.
   */
  identifiedPeriod?: Period;
  /**
   * Business identifier associated with the detected issue record.
   */
  identifier?: Identifier[];
  /**
   * There's an implicit constraint on the number of implicated resources based on DetectedIssue.type; e.g. For drug-drug, there would be more than one.  For timing, there would typically only be one.
   */
  implicated?: Reference[];
  /**
   * Indicates an action that has been taken or is committed to reduce or eliminate the likelihood of the risk identified by the detected issue from manifesting.  Can also reflect an observation of known mitigating factors that may reduce/eliminate the need for any action.
   */
  mitigation?: DetectedIssueMitigation[];
  /**
   * Indicates the patient whose record the detected issue is associated with.
   */
  patient?: Reference;
  /**
   * The literature, knowledge-base or similar reference that describes the propensity for the detected issue identified.
   */
  reference?: string;
  _reference?: Element;
  /**
   * Indicates the degree of importance associated with the identified issue based on the potential impact on the patient.
   */
  severity?: string;
  _severity?: Element;
  /**
   * This element is labeled as a modifier because the status contains the codes cancelled and entered-in-error that mark the issue as not currently valid.
   */
  status: string;
  _status?: Element;
}
/**
 * Code Values for the DetectedIssue.severity field
 */
export enum DetectedIssueSeverityCodes {
  HIGH = 'high',
  MODERATE = 'moderate',
  LOW = 'low',
}
/**
 * Code Values for the DetectedIssue.status field
 */
export enum DetectedIssueStatusCodes {
  REGISTERED = 'registered',
  PRELIMINARY = 'preliminary',
  FINAL = 'final',
  AMENDED = 'amended',
}
/**
 * UDI may identify an unique instance of a device, or it may only identify the type of the device.  See [UDI mappings](device-mappings.html#udi) for a complete mapping of UDI parts to Device.
 */
export interface DeviceUdiCarrier extends BackboneElement {
  /**
   * The AIDC form of UDIs should be scanned or otherwise used for the identification of the device whenever possible to minimize errors in records resulting from manual transcriptions. If separate barcodes for DI and PI are present, concatenate the string with DI first and in order of human readable expression on label.
   */
  carrierAIDC?: string;
  _carrierAIDC?: Element;
  /**
   * If separate barcodes for DI and PI are present, concatenate the string with DI first and in order of human readable expression on label.
   */
  carrierHRF?: string;
  _carrierHRF?: Element;
  /**
   * The device identifier (DI) is a mandatory, fixed portion of a UDI that identifies the labeler and the specific version or model of a device.
   */
  deviceIdentifier?: string;
  _deviceIdentifier?: Element;
  /**
   * A coded entry to indicate how the data was entered.
   */
  entryType?: string;
  _entryType?: Element;
  /**
   * Organization that is charged with issuing UDIs for devices.  For example, the US FDA issuers include :
   * 1) GS1:
   * http://hl7.org/fhir/NamingSystem/gs1-di,
   * 2) HIBCC:
   * http://hl7.org/fhir/NamingSystem/hibcc-dI,
   * 3) ICCBBA for blood containers:
   * http://hl7.org/fhir/NamingSystem/iccbba-blood-di,
   * 4) ICCBA for other devices:
   * http://hl7.org/fhir/NamingSystem/iccbba-other-di.
   */
  issuer?: string;
  _issuer?: Element;
  /**
   * The identity of the authoritative source for UDI generation within a  jurisdiction.  All UDIs are globally unique within a single namespace with the appropriate repository uri as the system.  For example,  UDIs of devices managed in the U.S. by the FDA, the value is  http://hl7.org/fhir/NamingSystem/fda-udi.
   */
  jurisdiction?: string;
  _jurisdiction?: Element;
}
/**
 * Code Values for the Device.udiCarrier.entryType field
 */
export enum DeviceUdiCarrierEntryTypeCodes {
  BARCODE = 'barcode',
  RFID = 'rfid',
  MANUAL = 'manual',
}
/**
 * This represents the manufacturer's name of the device as provided by the device, from a UDI label, or by a person describing the Device.  This typically would be used when a person provides the name(s) or when the device represents one of the names available from DeviceDefinition.
 */
export interface DeviceDeviceName extends BackboneElement {
  /**
   * The name of the device.
   */
  name: string;
  _name?: Element;
  /**
   * The type of deviceName.
   * UDILabelName | UserFriendlyName | PatientReportedName | ManufactureDeviceName | ModelName.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the Device.deviceName.type field
 */
export enum DeviceDeviceNameTypeCodes {
  UDI_LABEL_NAME = 'udi-label-name',
  USER_FRIENDLY_NAME = 'user-friendly-name',
  PATIENT_REPORTED_NAME = 'patient-reported-name',
  MANUFACTURER_NAME = 'manufacturer-name',
  MODEL_NAME = 'model-name',
  OTHER = 'other',
}
/**
 * The capabilities supported on a  device, the standards to which the device conforms for a particular purpose, and used for the communication.
 */
export interface DeviceSpecialization extends BackboneElement {
  /**
   * The standard that is used to operate and communicate.
   */
  systemType: CodeableConcept;
  /**
   * The version of the standard that is used to operate and communicate.
   */
  version?: string;
  _version?: Element;
}
/**
 * The actual design of the device or software version running on the device.
 */
export interface DeviceVersion extends BackboneElement {
  /**
   * A single component of the device version.
   */
  component?: Identifier;
  /**
   * The type of the device version.
   */
  type?: CodeableConcept;
  /**
   * The version text.
   */
  value: string;
  _value?: Element;
}
/**
 * The actual configuration settings of a device as it actually operates, e.g., regulation status, time properties.
 */
export interface DeviceProperty extends BackboneElement {
  /**
   * Code that specifies the property DeviceDefinitionPropetyCode (Extensible).
   */
  type: CodeableConcept;
  /**
   * Property value as a code, e.g., NTP4 (synced to NTP).
   */
  valueCode?: CodeableConcept[];
  /**
   * Property value as a quantity.
   */
  valueQuantity?: Quantity[];
}
/**
 * A type of a manufactured item that is used in the provision of healthcare without being substantially changed through that activity. The device may be a medical or non-medical device.
 */
export interface Device extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Device';
  /**
   * used for troubleshooting etc.
   */
  contact?: ContactPoint[];
  /**
   * The reference to the definition for the device.
   */
  definition?: Reference;
  /**
   * This represents the manufacturer's name of the device as provided by the device, from a UDI label, or by a person describing the Device.  This typically would be used when a person provides the name(s) or when the device represents one of the names available from DeviceDefinition.
   */
  deviceName?: DeviceDeviceName[];
  /**
   * For example, this applies to devices in the United States regulated under *Code of Federal Regulation 21CFR§1271.290(c)*.
   */
  distinctIdentifier?: string;
  _distinctIdentifier?: Element;
  /**
   * The date and time beyond which this device is no longer valid or should not be used (if applicable).
   */
  expirationDate?: string;
  _expirationDate?: Element;
  /**
   * The barcode string from a barcode present on a device label or package may identify the instance, include names given to the device in local usage, or may identify the type of device. If the identifier identifies the type of device, Device.type element should be used.
   */
  identifier?: Identifier[];
  /**
   * The place where the device can be found.
   */
  location?: Reference;
  /**
   * Lot number assigned by the manufacturer.
   */
  lotNumber?: string;
  _lotNumber?: Element;
  /**
   * The date and time when the device was manufactured.
   */
  manufactureDate?: string;
  _manufactureDate?: Element;
  /**
   * A name of the manufacturer.
   */
  manufacturer?: string;
  _manufacturer?: Element;
  /**
   * The model number for the device.
   */
  modelNumber?: string;
  _modelNumber?: Element;
  /**
   * Descriptive information, usage information or implantation information that is not captured in an existing element.
   */
  note?: Annotation[];
  /**
   * An organization that is responsible for the provision and ongoing maintenance of the device.
   */
  owner?: Reference;
  /**
   * The parent device.
   */
  parent?: Reference;
  /**
   * Alphanumeric Maximum 20.
   */
  partNumber?: string;
  _partNumber?: Element;
  /**
   * Patient information, If the device is affixed to a person.
   */
  patient?: Reference;
  /**
   * The actual configuration settings of a device as it actually operates, e.g., regulation status, time properties.
   */
  property?: DeviceProperty[];
  /**
   * Provides additional safety characteristics about a medical device.  For example devices containing latex.
   */
  safety?: CodeableConcept[];
  /**
   * Alphanumeric Maximum 20.
   */
  serialNumber?: string;
  _serialNumber?: Element;
  /**
   * The capabilities supported on a  device, the standards to which the device conforms for a particular purpose, and used for the communication.
   */
  specialization?: DeviceSpecialization[];
  /**
   * This element is labeled as a modifier because the status contains the codes inactive and entered-in-error that mark the device (record)as not currently valid.
   */
  status?: string;
  _status?: Element;
  /**
   * Reason for the dtatus of the Device availability.
   */
  statusReason?: CodeableConcept[];
  /**
   * The kind or type of device.
   */
  type?: CodeableConcept;
  /**
   * UDI may identify an unique instance of a device, or it may only identify the type of the device.  See [UDI mappings](device-mappings.html#udi) for a complete mapping of UDI parts to Device.
   */
  udiCarrier?: DeviceUdiCarrier[];
  /**
   * If the device is running a FHIR server, the network address should  be the Base URL from which a conformance statement may be retrieved.
   */
  url?: string;
  _url?: Element;
  /**
   * The actual design of the device or software version running on the device.
   */
  version?: DeviceVersion[];
}
/**
 * Code Values for the Device.status field
 */
export enum DeviceStatusCodes {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ENTERED_IN_ERROR = 'entered-in-error',
  UNKNOWN = 'unknown',
}
/**
 * Unique device identifier (UDI) assigned to device label or package.  Note that the Device may include multiple udiCarriers as it either may include just the udiCarrier for the jurisdiction it is sold, or for multiple jurisdictions it could have been sold.
 */
export interface DeviceDefinitionUdiDeviceIdentifier extends BackboneElement {
  /**
   * The identifier that is to be associated with every Device that references this DeviceDefintiion for the issuer and jurisdication porvided in the DeviceDefinition.udiDeviceIdentifier.
   */
  deviceIdentifier: string;
  _deviceIdentifier?: Element;
  /**
   * The organization that assigns the identifier algorithm.
   */
  issuer: string;
  _issuer?: Element;
  /**
   * The jurisdiction to which the deviceIdentifier applies.
   */
  jurisdiction: string;
  _jurisdiction?: Element;
}
/**
 * A name given to the device to identify it.
 */
export interface DeviceDefinitionDeviceName extends BackboneElement {
  /**
   * The name of the device.
   */
  name: string;
  _name?: Element;
  /**
   * The type of deviceName.
   * UDILabelName | UserFriendlyName | PatientReportedName | ManufactureDeviceName | ModelName.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the DeviceDefinition.deviceName.type field
 */
export enum DeviceDefinitionDeviceNameTypeCodes {
  UDI_LABEL_NAME = 'udi-label-name',
  USER_FRIENDLY_NAME = 'user-friendly-name',
  PATIENT_REPORTED_NAME = 'patient-reported-name',
  MANUFACTURER_NAME = 'manufacturer-name',
  MODEL_NAME = 'model-name',
  OTHER = 'other',
}
/**
 * The capabilities supported on a  device, the standards to which the device conforms for a particular purpose, and used for the communication.
 */
export interface DeviceDefinitionSpecialization extends BackboneElement {
  /**
   * The standard that is used to operate and communicate.
   */
  systemType: string;
  _systemType?: Element;
  /**
   * The version of the standard that is used to operate and communicate.
   */
  version?: string;
  _version?: Element;
}
/**
 * Device capabilities.
 */
export interface DeviceDefinitionCapability extends BackboneElement {
  /**
   * Description of capability.
   */
  description?: CodeableConcept[];
  /**
   * Type of capability.
   */
  type: CodeableConcept;
}
/**
 * The actual configuration settings of a device as it actually operates, e.g., regulation status, time properties.
 */
export interface DeviceDefinitionProperty extends BackboneElement {
  /**
   * Code that specifies the property DeviceDefinitionPropetyCode (Extensible).
   */
  type: CodeableConcept;
  /**
   * Property value as a code, e.g., NTP4 (synced to NTP).
   */
  valueCode?: CodeableConcept[];
  /**
   * Property value as a quantity.
   */
  valueQuantity?: Quantity[];
}
/**
 * A substance used to create the material(s) of which the device is made.
 */
export interface DeviceDefinitionMaterial extends BackboneElement {
  /**
   * Whether the substance is a known or suspected allergen.
   */
  allergenicIndicator?: boolean;
  _allergenicIndicator?: Element;
  /**
   * Indicates an alternative material of the device.
   */
  alternate?: boolean;
  _alternate?: Element;
  /**
   * The substance.
   */
  substance: CodeableConcept;
}
/**
 * The characteristics, operational status and capabilities of a medical-related component of a medical device.
 */
export interface DeviceDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'DeviceDefinition';
  /**
   * Device capabilities.
   */
  capability?: DeviceDefinitionCapability[];
  /**
   * used for troubleshooting etc.
   */
  contact?: ContactPoint[];
  /**
   * A name given to the device to identify it.
   */
  deviceName?: DeviceDefinitionDeviceName[];
  /**
   * Unique instance identifiers assigned to a device by the software, manufacturers, other organizations or owners. For example: handle ID.
   */
  identifier?: Identifier[];
  /**
   * Language code for the human-readable text strings produced by the device (all supported).
   */
  languageCode?: CodeableConcept[];
  /**
   * A name of the manufacturer.
   */
  manufacturerString?: string;
  _manufacturerString?: Element;
  /**
   * A name of the manufacturer.
   */
  manufacturerReference?: Reference;
  /**
   * A substance used to create the material(s) of which the device is made.
   */
  material?: DeviceDefinitionMaterial[];
  /**
   * The model number for the device.
   */
  modelNumber?: string;
  _modelNumber?: Element;
  /**
   * Descriptive information, usage information or implantation information that is not captured in an existing element.
   */
  note?: Annotation[];
  /**
   * Access to on-line information about the device.
   */
  onlineInformation?: string;
  _onlineInformation?: Element;
  /**
   * An organization that is responsible for the provision and ongoing maintenance of the device.
   */
  owner?: Reference;
  /**
   * The parent device it can be part of.
   */
  parentDevice?: Reference;
  /**
   * Dimensions, color etc.
   */
  physicalCharacteristics?: ProdCharacteristic;
  /**
   * The actual configuration settings of a device as it actually operates, e.g., regulation status, time properties.
   */
  property?: DeviceDefinitionProperty[];
  /**
   * The quantity of the device present in the packaging (e.g. the number of devices present in a pack, or the number of devices in the same package of the medicinal product).
   */
  quantity?: Quantity;
  /**
   * Safety characteristics of the device.
   */
  safety?: CodeableConcept[];
  /**
   * Shelf Life and storage information.
   */
  shelfLifeStorage?: ProductShelfLife[];
  /**
   * The capabilities supported on a  device, the standards to which the device conforms for a particular purpose, and used for the communication.
   */
  specialization?: DeviceDefinitionSpecialization[];
  /**
   * What kind of device or device system this is.
   */
  type?: CodeableConcept;
  /**
   * Unique device identifier (UDI) assigned to device label or package.  Note that the Device may include multiple udiCarriers as it either may include just the udiCarrier for the jurisdiction it is sold, or for multiple jurisdictions it could have been sold.
   */
  udiDeviceIdentifier?: DeviceDefinitionUdiDeviceIdentifier[];
  /**
   * If the device is running a FHIR server, the network address should  be the Base URL from which a conformance statement may be retrieved.
   */
  url?: string;
  _url?: Element;
  /**
   * The available versions of the device, e.g., software versions.
   */
  version?: string[];
  _version?: Element;
}
/**
 * Describes the calibrations that have been performed or that are required to be performed.
 */
export interface DeviceMetricCalibration extends BackboneElement {
  /**
   * Describes the state of the calibration.
   */
  state?: string;
  _state?: Element;
  /**
   * Describes the time last calibration has been performed.
   */
  time?: string;
  _time?: Element;
  /**
   * Describes the type of the calibration method.
   */
  type?: string;
  _type?: Element;
}
/**
 * Code Values for the DeviceMetric.calibration.state field
 */
export enum DeviceMetricCalibrationStateCodes {
  NOT_CALIBRATED = 'not-calibrated',
  CALIBRATION_REQUIRED = 'calibration-required',
  CALIBRATED = 'calibrated',
  UNSPECIFIED = 'unspecified',
}
/**
 * Code Values for the DeviceMetric.calibration.type field
 */
export enum DeviceMetricCalibrationTypeCodes {
  UNSPECIFIED = 'unspecified',
  OFFSET = 'offset',
  GAIN = 'gain',
  TWO_POINT = 'two-point',
}
/**
 * Describes a measurement, calculation or setting capability of a medical device.
 */
export interface DeviceMetric extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'DeviceMetric';
  /**
   * Describes the calibrations that have been performed or that are required to be performed.
   */
  calibration?: DeviceMetricCalibration[];
  /**
   * Indicates the category of the observation generation process. A DeviceMetric can be for example a setting, measurement, or calculation.
   */
  category: string;
  _category?: Element;
  /**
   * Describes the color representation for the metric. This is often used to aid clinicians to track and identify parameter types by color. In practice, consider a Patient Monitor that has ECG/HR and Pleth for example; the parameters are displayed in different characteristic colors, such as HR-blue, BP-green, and PR and SpO2- magenta.
   */
  color?: string;
  _color?: Element;
  /**
   * For identifiers assigned to a device by the device or gateway software, the `system` element of the identifier should be set to the unique identifier of the device.
   */
  identifier?: Identifier[];
  /**
   * Describes the measurement repetition time. This is not necessarily the same as the update period. The measurement repetition time can range from milliseconds up to hours. An example for a measurement repetition time in the range of milliseconds is the sampling rate of an ECG. An example for a measurement repetition time in the range of hours is a NIBP that is triggered automatically every hour. The update period may be different than the measurement repetition time, if the device does not update the published observed value with the same frequency as it was measured.
   */
  measurementPeriod?: Timing;
  /**
   * Indicates current operational state of the device. For example: On, Off, Standby, etc.
   */
  operationalStatus?: string;
  _operationalStatus?: Element;
  /**
   * Describes the link to the  Device that this DeviceMetric belongs to and that provide information about the location of this DeviceMetric in the containment structure of the parent Device. An example would be a Device that represents a Channel. This reference can be used by a client application to distinguish DeviceMetrics that have the same type, but should be interpreted based on their containment location.
   */
  parent?: Reference;
  /**
   * Describes the link to the  Device that this DeviceMetric belongs to and that contains administrative device information such as manufacturer, serial number, etc.
   */
  source?: Reference;
  /**
   * DeviceMetric.type can be referred to either IEEE 11073-10101 or LOINC.
   */
  type: CodeableConcept;
  /**
   * DeviceMetric.unit can refer to either UCUM or preferable a RTMMS coding system.
   */
  unit?: CodeableConcept;
}
/**
 * Code Values for the DeviceMetric.category field
 */
export enum DeviceMetricCategoryCodes {
  MEASUREMENT = 'measurement',
  SETTING = 'setting',
  CALCULATION = 'calculation',
  UNSPECIFIED = 'unspecified',
}
/**
 * Code Values for the DeviceMetric.color field
 */
export enum DeviceMetricColorCodes {
  BLACK = 'black',
  RED = 'red',
  GREEN = 'green',
  YELLOW = 'yellow',
  BLUE = 'blue',
  MAGENTA = 'magenta',
  CYAN = 'cyan',
  WHITE = 'white',
}
/**
 * Code Values for the DeviceMetric.operationalStatus field
 */
export enum DeviceMetricOperationalStatusCodes {
  ON = 'on',
  OFF = 'off',
  STANDBY = 'standby',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Specific parameters for the ordered item.  For example, the prism value for lenses.
 */
export interface DeviceRequestParameter extends BackboneElement {
  /**
   * A code or string that identifies the device detail being asserted.
   */
  code?: CodeableConcept;
  /**
   * Range means device should have a value that falls somewhere within the specified range.
   */
  valueCodeableConcept?: CodeableConcept;
  /**
   * Range means device should have a value that falls somewhere within the specified range.
   */
  valueQuantity?: Quantity;
  /**
   * Range means device should have a value that falls somewhere within the specified range.
   */
  valueRange?: Range;
  /**
   * Range means device should have a value that falls somewhere within the specified range.
   */
  valueBoolean?: boolean;
  _valueBoolean?: Element;
}
/**
 * Represents a request for a patient to employ a medical device. The device may be an implantable device, or an external assistive device, such as a walker.
 */
export interface DeviceRequest extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'DeviceRequest';
  /**
   * When the request transitioned to being actionable.
   */
  authoredOn?: string;
  _authoredOn?: Element;
  /**
   * Plan/proposal/order fulfilled by this request.
   */
  basedOn?: Reference[];
  /**
   * The details of the device to be used.
   */
  codeReference: Reference;
  /**
   * The details of the device to be used.
   */
  codeCodeableConcept: CodeableConcept;
  /**
   * An encounter that provides additional context in which this request is made.
   */
  encounter?: Reference;
  /**
   * Composite request this is part of.
   */
  groupIdentifier?: Identifier;
  /**
   * Identifiers assigned to this order by the orderer or by the receiver.
   */
  identifier?: Identifier[];
  /**
   * Note: This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  instantiatesCanonical?: string[];
  _instantiatesCanonical?: Element;
  /**
   * This might be an HTML page, PDF, etc. or could just be a non-resolvable URI identifier.
   */
  instantiatesUri?: string[];
  _instantiatesUri?: Element;
  /**
   * Insurance plans, coverage extensions, pre-authorizations and/or pre-determinations that may be required for delivering the requested service.
   */
  insurance?: Reference[];
  /**
   * Whether the request is a proposal, plan, an original order or a reflex order.
   */
  intent: string;
  _intent?: Element;
  /**
   * Details about this request that were not represented at all or sufficiently in one of the attributes provided in a class. These may include for example a comment, an instruction, or a note associated with the statement.
   */
  note?: Annotation[];
  /**
   * The timing schedule for the use of the device. The Schedule data type allows many different expressions, for example. "Every 8 hours"; "Three times a day"; "1/2 an hour before breakfast for 10 days from 23-Dec 2011:"; "15 Oct 2013, 17 Oct 2013 and 1 Nov 2013".
   */
  occurrenceDateTime?: string;
  _occurrenceDateTime?: Element;
  /**
   * The timing schedule for the use of the device. The Schedule data type allows many different expressions, for example. "Every 8 hours"; "Three times a day"; "1/2 an hour before breakfast for 10 days from 23-Dec 2011:"; "15 Oct 2013, 17 Oct 2013 and 1 Nov 2013".
   */
  occurrencePeriod?: Period;
  /**
   * The timing schedule for the use of the device. The Schedule data type allows many different expressions, for example. "Every 8 hours"; "Three times a day"; "1/2 an hour before breakfast for 10 days from 23-Dec 2011:"; "15 Oct 2013, 17 Oct 2013 and 1 Nov 2013".
   */
  occurrenceTiming?: Timing;
  /**
   * Specific parameters for the ordered item.  For example, the prism value for lenses.
   */
  parameter?: DeviceRequestParameter[];
  /**
   * The desired performer for doing the diagnostic testing.
   */
  performer?: Reference;
  /**
   * Desired type of performer for doing the diagnostic testing.
   */
  performerType?: CodeableConcept;
  /**
   * Indicates how quickly the {{title}} should be addressed with respect to other requests.
   */
  priority?: string;
  _priority?: Element;
  /**
   * The request takes the place of the referenced completed or terminated request(s).
   */
  priorRequest?: Reference[];
  /**
   * Reason or justification for the use of this device.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Reason or justification for the use of this device.
   */
  reasonReference?: Reference[];
  /**
   * This might not include provenances for all versions of the request - only those deemed "relevant" or important.
   * This SHALL NOT include the Provenance associated with this current version of the resource.  (If that provenance is deemed to be a "relevant" change, it will need to be added as part of a later update.  Until then, it can be queried directly as the Provenance that points to this version using _revinclude
   * All Provenances should have some historical version of this Request as their subject.
   */
  relevantHistory?: Reference[];
  /**
   * The individual who initiated the request and has responsibility for its activation.
   */
  requester?: Reference;
  /**
   * This element is labeled as a modifier because the status contains the codes cancelled and entered-in-error that mark the request as not currently valid.
   */
  status?: string;
  _status?: Element;
  /**
   * The patient who will use the device.
   */
  subject: Reference;
  /**
   * Additional clinical information about the patient that may influence the request fulfilment.  For example, this may include where on the subject's body the device will be used (i.e. the target site).
   */
  supportingInfo?: Reference[];
}
/**
 * Code Values for the DeviceRequest.intent field
 */
export enum DeviceRequestIntentCodes {
  PROPOSAL = 'proposal',
  PLAN = 'plan',
  DIRECTIVE = 'directive',
  ORDER = 'order',
  ORIGINAL_ORDER = 'original-order',
  REFLEX_ORDER = 'reflex-order',
  FILLER_ORDER = 'filler-order',
  INSTANCE_ORDER = 'instance-order',
  OPTION = 'option',
}
/**
 * Code Values for the DeviceRequest.priority field
 */
export enum DeviceRequestPriorityCodes {
  ROUTINE = 'routine',
  URGENT = 'urgent',
  ASAP = 'asap',
  STAT = 'stat',
}
/**
 * Code Values for the DeviceRequest.status field
 */
export enum DeviceRequestStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  ON_HOLD = 'on-hold',
  REVOKED = 'revoked',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  UNKNOWN = 'unknown',
}
/**
 * A record of a device being used by a patient where the record is the result of a report from the patient or another clinician.
 */
export interface DeviceUseStatement extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'DeviceUseStatement';
  /**
   * A plan, proposal or order that is fulfilled in whole or in part by this DeviceUseStatement.
   */
  basedOn?: Reference[];
  /**
   * Indicates the anotomic location on the subject's body where the device was used ( i.e. the target).
   */
  bodySite?: CodeableConcept;
  /**
   * The most common use cases for deriving a DeviceUseStatement comes from creating it from a request or from an observation or a claim. it should be noted that the amount of information that is available varies from the type resource that you derive the DeviceUseStatement from.
   */
  derivedFrom?: Reference[];
  /**
   * The details of the device used.
   */
  device: Reference;
  /**
   * An external identifier for this statement such as an IRI.
   */
  identifier?: Identifier[];
  /**
   * Details about the device statement that were not represented at all or sufficiently in one of the attributes provided in a class. These may include for example a comment, an instruction, or a note associated with the statement.
   */
  note?: Annotation[];
  /**
   * Reason or justification for the use of the device.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Indicates another resource whose existence justifies this DeviceUseStatement.
   */
  reasonReference?: Reference[];
  /**
   * The time at which the statement was made/recorded.
   */
  recordedOn?: string;
  _recordedOn?: Element;
  /**
   * Who reported the device was being used by the patient.
   */
  source?: Reference;
  /**
   * DeviceUseStatment is a statement at a point in time.  The status is only representative at the point when it was asserted.  The value set for contains codes that assert the status of the use  by the patient (for example, stopped or on hold) as well as codes that assert the status of the resource itself (for example, entered in error).
   * This element is labeled as a modifier because the status contains the codes that mark the statement as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * The patient who used the device.
   */
  subject: Reference;
  /**
   * How often the device was used.
   */
  timingTiming?: Timing;
  /**
   * How often the device was used.
   */
  timingPeriod?: Period;
  /**
   * How often the device was used.
   */
  timingDateTime?: string;
  _timingDateTime?: Element;
}
/**
 * Code Values for the DeviceUseStatement.status field
 */
export enum DeviceUseStatementStatusCodes {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * A list of key images associated with this report. The images are generally created during the diagnostic process, and may be directly of the patient, or of treated specimens (i.e. slides of interest).
 */
export interface DiagnosticReportMedia extends BackboneElement {
  /**
   * The comment should be displayed with the image. It would be common for the report to include additional discussion of the image contents in other sections such as the conclusion.
   */
  comment?: string;
  _comment?: Element;
  /**
   * Reference to the image source.
   */
  link: Reference;
}
/**
 * The findings and interpretation of diagnostic  tests performed on patients, groups of patients, devices, and locations, and/or specimens derived from these. The report includes clinical context such as requesting and provider information, and some mix of atomic results, images, textual and coded interpretations, and formatted representation of diagnostic reports.
 */
export interface DiagnosticReport extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'DiagnosticReport';
  /**
   * Note: Usually there is one test request for each result, however in some circumstances multiple test requests may be represented using a single test result resource. Note that there are also cases where one request leads to multiple reports.
   */
  basedOn?: Reference[];
  /**
   * Multiple categories are allowed using various categorization schemes.   The level of granularity is defined by the category concepts in the value set. More fine-grained filtering can be performed using the metadata and/or terminology hierarchy in DiagnosticReport.code.
   */
  category?: CodeableConcept[];
  /**
   * A code or name that describes this diagnostic report.
   */
  code: CodeableConcept;
  /**
   * Concise and clinically contextualized summary conclusion (interpretation/impression) of the diagnostic report.
   */
  conclusion?: string;
  _conclusion?: Element;
  /**
   * One or more codes that represent the summary conclusion (interpretation/impression) of the diagnostic report.
   */
  conclusionCode?: CodeableConcept[];
  /**
   * If the diagnostic procedure was performed on the patient, this is the time it was performed. If there are specimens, the diagnostically relevant time can be derived from the specimen collection times, but the specimen information is not always available, and the exact relationship between the specimens and the diagnostically relevant time is not always automatic.
   */
  effectiveDateTime?: string;
  _effectiveDateTime?: Element;
  /**
   * If the diagnostic procedure was performed on the patient, this is the time it was performed. If there are specimens, the diagnostically relevant time can be derived from the specimen collection times, but the specimen information is not always available, and the exact relationship between the specimens and the diagnostically relevant time is not always automatic.
   */
  effectivePeriod?: Period;
  /**
   * This will typically be the encounter the event occurred within, but some events may be initiated prior to or after the official completion of an encounter  but still be tied to the context of the encounter  (e.g. pre-admission laboratory tests).
   */
  encounter?: Reference;
  /**
   * Usually assigned by the Information System of the diagnostic service provider (filler id).
   */
  identifier?: Identifier[];
  /**
   * ImagingStudy and the image element are somewhat overlapping - typically, the list of image references in the image element will also be found in one of the imaging study resources. However, each caters to different types of displays for different types of purposes. Neither, either, or both may be provided.
   */
  imagingStudy?: Reference[];
  /**
   * May be different from the update time of the resource itself, because that is the status of the record (potentially a secondary copy), not the actual release time of the report.
   */
  issued?: string;
  _issued?: Element;
  /**
   * A list of key images associated with this report. The images are generally created during the diagnostic process, and may be directly of the patient, or of treated specimens (i.e. slides of interest).
   */
  media?: DiagnosticReportMedia[];
  /**
   * This is not necessarily the source of the atomic data items or the entity that interpreted the results. It is the entity that takes responsibility for the clinical report.
   */
  performer?: Reference[];
  /**
   * "application/pdf" is recommended as the most reliable and interoperable in this context.
   */
  presentedForm?: Attachment[];
  /**
   * Observations can contain observations.
   */
  result?: Reference[];
  /**
   * Might not be the same entity that takes responsibility for the clinical report.
   */
  resultsInterpreter?: Reference[];
  /**
   * If the specimen is sufficiently specified with a code in the test result name, then this additional data may be redundant. If there are multiple specimens, these may be represented per observation or group.
   */
  specimen?: Reference[];
  /**
   * The status of the diagnostic report.
   */
  status: string;
  _status?: Element;
  /**
   * The subject of the report. Usually, but not always, this is a patient. However, diagnostic services also perform analyses on specimens collected from a variety of other sources.
   */
  subject?: Reference;
}
/**
 * Code Values for the DiagnosticReport.status field
 */
export enum DiagnosticReportStatusCodes {
  REGISTERED = 'registered',
  PARTIAL = 'partial',
  PRELIMINARY = 'preliminary',
  FINAL = 'final',
}
/**
 * May be identifiers or resources that caused the DocumentManifest to be created.
 */
export interface DocumentManifestRelated extends BackboneElement {
  /**
   * If both identifier and ref elements are present they shall refer to the same thing.
   */
  identifier?: Identifier;
  /**
   * If both identifier and ref elements are present they shall refer to the same thing.
   */
  ref?: Reference;
}
/**
 * A collection of documents compiled for a purpose together with metadata that applies to the collection.
 */
export interface DocumentManifest extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'DocumentManifest';
  /**
   * Not necessarily who did the actual data entry (i.e. typist) or who was the source (informant).
   */
  author?: Reference[];
  /**
   * When used for XDS the intended focus of the DocumentManifest is for the reference to target to be a set of DocumentReference Resources. The reference is to "Any" to support EN 13606 usage, where an extract is DocumentManifest that references  List and Composition resources.
   */
  content: Reference[];
  /**
   * Creation time is used for tracking, organizing versions and searching. This is the creation time of the document set, not the documents on which it is based.
   */
  created?: string;
  _created?: Element;
  /**
   * What the document is about, rather than a terse summary of the document. It is commonly the case that records do not have a title and are collectively referred to by the display name of Record code (e.g. a "consultation" or "progress note").
   */
  description?: string;
  _description?: Element;
  /**
   * Other identifiers associated with the document manifest, including version independent  identifiers.
   */
  identifier?: Identifier[];
  /**
   * A single identifier that uniquely identifies this manifest. Principally used to refer to the manifest in non-FHIR contexts.
   */
  masterIdentifier?: Identifier;
  /**
   * How the recipient receives the document set or is notified of it is up to the implementation. This element is just a statement of intent. If the recipient is a person, and it is not known whether the person is a patient or a practitioner, RelatedPerson would be the default choice.
   */
  recipient?: Reference[];
  /**
   * May be identifiers or resources that caused the DocumentManifest to be created.
   */
  related?: DocumentManifestRelated[];
  /**
   * Identifies the source system, application, or software that produced the document manifest.
   */
  source?: string;
  _source?: Element;
  /**
   * This element is labeled as a modifier because the status contains the codes that mark the manifest as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * Who or what the set of documents is about. The documents can be about a person, (patient or healthcare practitioner), a device (i.e. machine) or even a group of subjects (such as a document about a herd of farm animals, or a set of patients that share a common exposure). If the documents cross more than one subject, then more than one subject is allowed here (unusual use case).
   */
  subject?: Reference;
  /**
   * Specifies the kind of this set of documents (e.g. Patient Summary, Discharge Summary, Prescription, etc.). The type of a set of documents may be the same as one of the documents in it - especially if there is only one - but it may be wider.
   */
  type?: CodeableConcept;
}
/**
 * Code Values for the DocumentManifest.status field
 */
export enum DocumentManifestStatusCodes {
  CURRENT = 'current',
  SUPERSEDED = 'superseded',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * This element is labeled as a modifier because documents that append to other documents are incomplete on their own.
 */
export interface DocumentReferenceRelatesTo extends BackboneElement {
  /**
   * If this document appends another document, then the document cannot be fully understood without also accessing the referenced document.
   */
  code: string;
  _code?: Element;
  /**
   * The target document of this relationship.
   */
  target: Reference;
}
/**
 * Code Values for the DocumentReference.relatesTo.code field
 */
export enum DocumentReferenceRelatesToCodeCodes {
  REPLACES = 'replaces',
  TRANSFORMS = 'transforms',
  SIGNS = 'signs',
  APPENDS = 'appends',
}
/**
 * The document and format referenced. There may be multiple content element repetitions, each with a different format.
 */
export interface DocumentReferenceContent extends BackboneElement {
  /**
   * The document or URL of the document along with critical metadata to prove content has integrity.
   */
  attachment: Attachment;
  /**
   * Note that while IHE mostly issues URNs for format types, not all documents can be identified by a URI.
   */
  format?: Coding;
}
/**
 * These values are primarily added to help with searching for interesting/relevant documents.
 */
export interface DocumentReferenceContext extends BackboneElement {
  /**
   * Describes the clinical encounter or type of care that the document content is associated with.
   */
  encounter?: Reference[];
  /**
   * An event can further specialize the act inherent in the type, such as  where it is simply "Procedure Report" and the procedure was a "colonoscopy". If one or more event codes are included, they shall not conflict with the values inherent in the class or type elements as such a conflict would create an ambiguous situation.
   */
  event?: CodeableConcept[];
  /**
   * The kind of facility where the patient was seen.
   */
  facilityType?: CodeableConcept;
  /**
   * The time period over which the service that is described by the document was provided.
   */
  period?: Period;
  /**
   * This element should be based on a coarse classification system for the class of specialty practice. Recommend the use of the classification system for Practice Setting, such as that described by the Subject Matter Domain in LOINC.
   */
  practiceSetting?: CodeableConcept;
  /**
   * May be identifiers or resources that caused the DocumentReference or referenced Document to be created.
   */
  related?: Reference[];
  /**
   * The Patient Information as known when the document was published. May be a reference to a version specific, or contained.
   */
  sourcePatientInfo?: Reference;
}
/**
 * A reference to a document of any kind for any purpose. Provides metadata about the document so that the document can be discovered and managed. The scope of a document is any seralized object with a mime-type, so includes formal patient centric documents (CDA), cliical notes, scanned paper, and non-patient specific documents like policy text.
 */
export interface DocumentReference extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'DocumentReference';
  /**
   * Represents a participant within the author institution who has legally authenticated or attested the document. Legal authentication implies that a document has been signed manually or electronically by the legal Authenticator.
   */
  authenticator?: Reference;
  /**
   * Not necessarily who did the actual data entry (i.e. typist) or who was the source (informant).
   */
  author?: Reference[];
  /**
   * Key metadata element describing the the category or classification of the document. This is a broader perspective that groups similar documents based on how they would be used. This is a primary key used in searching.
   */
  category?: CodeableConcept[];
  /**
   * The document and format referenced. There may be multiple content element repetitions, each with a different format.
   */
  content: DocumentReferenceContent[];
  /**
   * These values are primarily added to help with searching for interesting/relevant documents.
   */
  context?: DocumentReferenceContext;
  /**
   * Identifies the logical organization (software system, vendor, or department) to go to find the current version, where to report issues, etc. This is different from the physical location (URL, disk drive, or server) of the document, which is the technical location of the document, which host may be delegated to the management of some other organization.
   */
  custodian?: Reference;
  /**
   * Referencing/indexing time is used for tracking, organizing versions and searching.
   */
  date?: string;
  _date?: Element;
  /**
   * What the document is about,  a terse summary of the document.
   */
  description?: string;
  _description?: Element;
  /**
   * The document that is pointed to might be in various lifecycle states.
   */
  docStatus?: string;
  _docStatus?: Element;
  /**
   * Other identifiers associated with the document, including version independent identifiers.
   */
  identifier?: Identifier[];
  /**
   * CDA Document Id extension and root.
   */
  masterIdentifier?: Identifier;
  /**
   * This element is labeled as a modifier because documents that append to other documents are incomplete on their own.
   */
  relatesTo?: DocumentReferenceRelatesTo[];
  /**
   * The confidentiality codes can carry multiple vocabulary items. HL7 has developed an understanding of security and privacy tags that might be desirable in a Document Sharing environment, called HL7 Healthcare Privacy and Security Classification System (HCS). The following specification is recommended but not mandated, as the vocabulary bindings are an administrative domain responsibility. The use of this method is up to the policy domain such as the XDS Affinity Domain or other Trust Domain where all parties including sender and recipients are trusted to appropriately tag and enforce.
   * In the HL7 Healthcare Privacy and Security Classification (HCS) there are code systems specific to Confidentiality, Sensitivity, Integrity, and Handling Caveats. Some values would come from a local vocabulary as they are related to workflow roles and special projects.
   */
  securityLabel?: CodeableConcept[];
  /**
   * This is the status of the DocumentReference object, which might be independent from the docStatus element.
   * This element is labeled as a modifier because the status contains the codes that mark the document or reference as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * Who or what the document is about. The document can be about a person, (patient or healthcare practitioner), a device (e.g. a machine) or even a group of subjects (such as a document about a herd of farm animals, or a set of patients that share a common exposure).
   */
  subject?: Reference;
  /**
   * Key metadata element describing the document that describes he exact type of document. Helps humans to assess whether the document is of interest when viewing a list of documents.
   */
  type?: CodeableConcept;
}
/**
 * Code Values for the DocumentReference.docStatus field
 */
export enum DocumentReferenceDocStatusCodes {
  PRELIMINARY = 'preliminary',
  FINAL = 'final',
  AMENDED = 'amended',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Code Values for the DocumentReference.status field
 */
export enum DocumentReferenceStatusCodes {
  CURRENT = 'current',
  SUPERSEDED = 'superseded',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * A resource that includes narrative, extensions, and contained resources.
 */
export interface DomainResource extends Resource {
  /**
   * This should never be done when the content can be identified properly, as once identification is lost, it is extremely difficult (and context dependent) to restore it again. Contained resources may have profiles and tags In their meta elements, but SHALL NOT have security labels.
   */
  contained?: Resource[];
  /**
   * There can be no stigma associated with the use of extensions by any application, project, or standard - regardless of the institution or jurisdiction that uses or defines the extensions.  The use of extensions is what allows the FHIR specification to retain a core level of simplicity for everyone.
   */
  extension?: Extension[];
  /**
   * There can be no stigma associated with the use of extensions by any application, project, or standard - regardless of the institution or jurisdiction that uses or defines the extensions.  The use of extensions is what allows the FHIR specification to retain a core level of simplicity for everyone.
   */
  modifierExtension?: Extension[];
  /**
   * Contained resources do not have narrative. Resources that are not contained SHOULD have a narrative. In some cases, a resource may only have text with little or no additional discrete data (as long as all minOccurs=1 elements are satisfied).  This may be necessary for data from legacy systems where information is captured as a "text blob" or where text is additionally entered raw or narrated and encoded information is added later.
   */
  text?: Narrative;
}
/**
 * A description of the size of the sample involved in the synthesis.
 */
export interface EffectEvidenceSynthesisSampleSize extends BackboneElement {
  /**
   * Human-readable summary of sample size.
   */
  description?: string;
  _description?: Element;
  /**
   * Number of participants included in this evidence synthesis.
   */
  numberOfParticipants?: number;
  /**
   * Number of studies included in this evidence synthesis.
   */
  numberOfStudies?: number;
}
/**
 * A description of the results for each exposure considered in the effect estimate.
 */
export interface EffectEvidenceSynthesisResultsByExposure extends BackboneElement {
  /**
   * Human-readable summary of results by exposure state.
   */
  description?: string;
  _description?: Element;
  /**
   * Whether these results are for the exposure state or alternative exposure state.
   */
  exposureState?: string;
  _exposureState?: Element;
  /**
   * Reference to a RiskEvidenceSynthesis resource.
   */
  riskEvidenceSynthesis: Reference;
  /**
   * Used to define variant exposure states such as low-risk state.
   */
  variantState?: CodeableConcept;
}
/**
 * Code Values for the EffectEvidenceSynthesis.resultsByExposure.exposureState field
 */
export enum EffectEvidenceSynthesisResultsByExposureExposureStateCodes {
  EXPOSURE = 'exposure',
  EXPOSURE_ALTERNATIVE = 'exposure-alternative',
}
/**
 * A description of the precision of the estimate for the effect.
 */
export interface EffectEvidenceSynthesisEffectEstimatePrecisionEstimate extends BackboneElement {
  /**
   * Lower bound of confidence interval.
   */
  from?: number;
  /**
   * Use 95 for a 95% confidence interval.
   */
  level?: number;
  /**
   * Upper bound of confidence interval.
   */
  to?: number;
  /**
   * Examples include confidence interval and interquartile range.
   */
  type?: CodeableConcept;
}
/**
 * The estimated effect of the exposure variant.
 */
export interface EffectEvidenceSynthesisEffectEstimate extends BackboneElement {
  /**
   * Human-readable summary of effect estimate.
   */
  description?: string;
  _description?: Element;
  /**
   * A description of the precision of the estimate for the effect.
   */
  precisionEstimate?: EffectEvidenceSynthesisEffectEstimatePrecisionEstimate[];
  /**
   * Examples include relative risk and mean difference.
   */
  type?: CodeableConcept;
  /**
   * Specifies the UCUM unit for the outcome.
   */
  unitOfMeasure?: CodeableConcept;
  /**
   * The point estimate of the effect estimate.
   */
  value?: number;
  /**
   * Used to define variant exposure states such as low-risk state.
   */
  variantState?: CodeableConcept;
}
/**
 * A description of a component of the overall certainty.
 */
export interface EffectEvidenceSynthesisCertaintyCertaintySubcomponent extends BackboneElement {
  /**
   * A human-readable string to clarify or explain concepts about the resource.
   */
  note?: Annotation[];
  /**
   * A rating of a subcomponent of rating certainty.
   */
  rating?: CodeableConcept[];
  /**
   * Type of subcomponent of certainty rating.
   */
  type?: CodeableConcept;
}
/**
 * A description of the certainty of the effect estimate.
 */
export interface EffectEvidenceSynthesisCertainty extends BackboneElement {
  /**
   * A description of a component of the overall certainty.
   */
  certaintySubcomponent?: EffectEvidenceSynthesisCertaintyCertaintySubcomponent[];
  /**
   * A human-readable string to clarify or explain concepts about the resource.
   */
  note?: Annotation[];
  /**
   * A rating of the certainty of the effect estimate.
   */
  rating?: CodeableConcept[];
}
/**
 * The EffectEvidenceSynthesis resource describes the difference in an outcome between exposures states in a population where the effect estimate is derived from a combination of research studies.
 */
export interface EffectEvidenceSynthesis extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'EffectEvidenceSynthesis';
  /**
   * The 'date' element may be more recent than the approval date because of minor changes or editorial corrections.
   */
  approvalDate?: string;
  _approvalDate?: Element;
  /**
   * An individiual or organization primarily involved in the creation and maintenance of the content.
   */
  author?: ContactDetail[];
  /**
   * A description of the certainty of the effect estimate.
   */
  certainty?: EffectEvidenceSynthesisCertainty[];
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the effect evidence synthesis and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the effect evidence synthesis.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the effect evidence synthesis. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the effect evidence synthesis was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the effect evidence synthesis as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the effect evidence synthesis is presumed to be the predominant language in the place the effect evidence synthesis was created).
   */
  description?: string;
  _description?: Element;
  /**
   * An individual or organization primarily responsible for internal coherence of the content.
   */
  editor?: ContactDetail[];
  /**
   * The estimated effect of the exposure variant.
   */
  effectEstimate?: EffectEvidenceSynthesisEffectEstimate[];
  /**
   * The effective period for a effect evidence synthesis  determines when the content is applicable for usage and is independent of publication and review dates. For example, a measure intended to be used for the year 2016 might be published in 2015.
   */
  effectivePeriod?: Period;
  /**
   * An individual or organization responsible for officially endorsing the content for use in some setting.
   */
  endorser?: ContactDetail[];
  /**
   * A reference to a EvidenceVariable resource that defines the exposure for the research.
   */
  exposure: Reference;
  /**
   * A reference to a EvidenceVariable resource that defines the comparison exposure for the research.
   */
  exposureAlternative: Reference;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this effect evidence synthesis outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * It may be possible for the effect evidence synthesis to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * If specified, this date follows the original approval date.
   */
  lastReviewDate?: string;
  _lastReviewDate?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * A human-readable string to clarify or explain concepts about the resource.
   */
  note?: Annotation[];
  /**
   * A reference to a EvidenceVariable resomece that defines the outcome for the research.
   */
  outcome: Reference;
  /**
   * A reference to a EvidenceVariable resource that defines the population for the research.
   */
  population: Reference;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the effect evidence synthesis is the organization or individual primarily responsible for the maintenance and upkeep of the effect evidence synthesis. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the effect evidence synthesis. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * Each related artifact is either an attachment, or a reference to another resource, but not both.
   */
  relatedArtifact?: RelatedArtifact[];
  /**
   * A description of the results for each exposure considered in the effect estimate.
   */
  resultsByExposure?: EffectEvidenceSynthesisResultsByExposure[];
  /**
   * An individual or organization primarily responsible for review of some aspect of the content.
   */
  reviewer?: ContactDetail[];
  /**
   * A description of the size of the sample involved in the synthesis.
   */
  sampleSize?: EffectEvidenceSynthesisSampleSize;
  /**
   * Allows filtering of effect evidence synthesiss that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * Type of study eg randomized trial.
   */
  studyType?: CodeableConcept;
  /**
   * Type of synthesis eg meta-analysis.
   */
  synthesisType?: CodeableConcept;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Descriptive topics related to the content of the EffectEvidenceSynthesis. Topics provide a high-level categorization grouping types of EffectEvidenceSynthesiss that can be useful for filtering and searching.
   */
  topic?: CodeableConcept[];
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different effect evidence synthesis instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the effect evidence synthesis with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the EffectEvidenceSynthesis.status field
 */
export enum EffectEvidenceSynthesisStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * The current status is always found in the current version of the resource, not the status history.
 */
export interface EncounterStatusHistory extends BackboneElement {
  /**
   * The time that the episode was in the specified status.
   */
  period: Period;
  /**
   * planned | arrived | triaged | in-progress | onleave | finished | cancelled +.
   */
  status: string;
  _status?: Element;
}
/**
 * Code Values for the Encounter.statusHistory.status field
 */
export enum EncounterStatusHistoryStatusCodes {
  PLANNED = 'planned',
  ARRIVED = 'arrived',
  TRIAGED = 'triaged',
  IN_PROGRESS = 'in-progress',
  ONLEAVE = 'onleave',
  FINISHED = 'finished',
  CANCELLED = 'cancelled',
}
/**
 * The class history permits the tracking of the encounters transitions without needing to go  through the resource history.  This would be used for a case where an admission starts of as an emergency encounter, then transitions into an inpatient scenario. Doing this and not restarting a new encounter ensures that any lab/diagnostic results can more easily follow the patient and not require re-processing and not get lost or cancelled during a kind of discharge from emergency to inpatient.
 */
export interface EncounterClassHistory extends BackboneElement {
  /**
   * inpatient | outpatient | ambulatory | emergency +.
   */
  class: Coding;
  /**
   * The time that the episode was in the specified class.
   */
  period: Period;
}
/**
 * The list of people responsible for providing the service.
 */
export interface EncounterParticipant extends BackboneElement {
  /**
   * Persons involved in the encounter other than the patient.
   */
  individual?: Reference;
  /**
   * The period of time that the specified participant participated in the encounter. These can overlap or be sub-sets of the overall encounter's period.
   */
  period?: Period;
  /**
   * The participant type indicates how an individual participates in an encounter. It includes non-practitioner participants, and for practitioners this is to describe the action type in the context of this encounter (e.g. Admitting Dr, Attending Dr, Translator, Consulting Dr). This is different to the practitioner roles which are functional roles, derived from terms of employment, education, licensing, etc.
   */
  type?: CodeableConcept[];
}
/**
 * The list of diagnosis relevant to this encounter.
 */
export interface EncounterDiagnosis extends BackboneElement {
  /**
   * For systems that need to know which was the primary diagnosis, these will be marked with the standard extension primaryDiagnosis (which is a sequence value rather than a flag, 1 = primary diagnosis).
   */
  condition: Reference;
  /**
   * Ranking of the diagnosis (for each role type).
   */
  rank?: number;
  /**
   * Role that this diagnosis has within the encounter (e.g. admission, billing, discharge …).
   */
  use?: CodeableConcept;
}
/**
 * An Encounter may cover more than just the inpatient stay. Contexts such as outpatients, community clinics, and aged care facilities are also included.
 * The duration recorded in the period of this encounter covers the entire scope of this hospitalization record.
 */
export interface EncounterHospitalization extends BackboneElement {
  /**
   * From where patient was admitted (physician referral, transfer).
   */
  admitSource?: CodeableConcept;
  /**
   * Location/organization to which the patient is discharged.
   */
  destination?: Reference;
  /**
   * For example, a patient may request both a dairy-free and nut-free diet preference (not mutually exclusive).
   */
  dietPreference?: CodeableConcept[];
  /**
   * Category or kind of location after discharge.
   */
  dischargeDisposition?: CodeableConcept;
  /**
   * The location/organization from which the patient came before admission.
   */
  origin?: Reference;
  /**
   * Pre-admission identifier.
   */
  preAdmissionIdentifier?: Identifier;
  /**
   * Whether this hospitalization is a readmission and why if known.
   */
  reAdmission?: CodeableConcept;
  /**
   * Any special requests that have been made for this hospitalization encounter, such as the provision of specific equipment or other things.
   */
  specialArrangement?: CodeableConcept[];
  /**
   * Special courtesies (VIP, board member).
   */
  specialCourtesy?: CodeableConcept[];
}
/**
 * Virtual encounters can be recorded in the Encounter by specifying a location reference to a location of type "kind" such as "client's home" and an encounter.class = "virtual".
 */
export interface EncounterLocation extends BackboneElement {
  /**
   * The location where the encounter takes place.
   */
  location: Reference;
  /**
   * Time period during which the patient was present at the location.
   */
  period?: Period;
  /**
   * This information is de-normalized from the Location resource to support the easier understanding of the encounter resource and processing in messaging or query.
   * There may be many levels in the hierachy, and this may only pic specific levels that are required for a specific usage scenario.
   */
  physicalType?: CodeableConcept;
  /**
   * When the patient is no longer active at a location, then the period end date is entered, and the status may be changed to completed.
   */
  status?: string;
  _status?: Element;
}
/**
 * Code Values for the Encounter.location.status field
 */
export enum EncounterLocationStatusCodes {
  PLANNED = 'planned',
  ACTIVE = 'active',
  RESERVED = 'reserved',
  COMPLETED = 'completed',
}
/**
 * An interaction between a patient and healthcare provider(s) for the purpose of providing healthcare service(s) or assessing the health status of a patient.
 */
export interface Encounter extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Encounter';
  /**
   * The billing system may choose to allocate billable items associated with the Encounter to different referenced Accounts based on internal business rules.
   */
  account?: Reference[];
  /**
   * The appointment that scheduled this encounter.
   */
  appointment?: Reference[];
  /**
   * The request this encounter satisfies (e.g. incoming referral or procedure request).
   */
  basedOn?: Reference[];
  /**
   * Concepts representing classification of patient encounter such as ambulatory (outpatient), inpatient, emergency, home health or others due to local variations.
   */
  class: Coding;
  /**
   * The class history permits the tracking of the encounters transitions without needing to go  through the resource history.  This would be used for a case where an admission starts of as an emergency encounter, then transitions into an inpatient scenario. Doing this and not restarting a new encounter ensures that any lab/diagnostic results can more easily follow the patient and not require re-processing and not get lost or cancelled during a kind of discharge from emergency to inpatient.
   */
  classHistory?: EncounterClassHistory[];
  /**
   * The list of diagnosis relevant to this encounter.
   */
  diagnosis?: EncounterDiagnosis[];
  /**
   * Where a specific encounter should be classified as a part of a specific episode(s) of care this field should be used. This association can facilitate grouping of related encounters together for a specific purpose, such as government reporting, issue tracking, association via a common problem.  The association is recorded on the encounter as these are typically created after the episode of care and grouped on entry rather than editing the episode of care to append another encounter to it (the episode of care could span years).
   */
  episodeOfCare?: Reference[];
  /**
   * An Encounter may cover more than just the inpatient stay. Contexts such as outpatients, community clinics, and aged care facilities are also included.
   * The duration recorded in the period of this encounter covers the entire scope of this hospitalization record.
   */
  hospitalization?: EncounterHospitalization;
  /**
   * Identifier(s) by which this encounter is known.
   */
  identifier?: Identifier[];
  /**
   * May differ from the time the Encounter.period lasted because of leave of absence.
   */
  length?: Duration;
  /**
   * Virtual encounters can be recorded in the Encounter by specifying a location reference to a location of type "kind" such as "client's home" and an encounter.class = "virtual".
   */
  location?: EncounterLocation[];
  /**
   * The list of people responsible for providing the service.
   */
  participant?: EncounterParticipant[];
  /**
   * This is also used for associating a child's encounter back to the mother's encounter.
   * Refer to the Notes section in the Patient resource for further details.
   */
  partOf?: Reference;
  /**
   * If not (yet) known, the end of the Period may be omitted.
   */
  period?: Period;
  /**
   * Indicates the urgency of the encounter.
   */
  priority?: CodeableConcept;
  /**
   * For systems that need to know which was the primary diagnosis, these will be marked with the standard extension primaryDiagnosis (which is a sequence value rather than a flag, 1 = primary diagnosis).
   */
  reasonCode?: CodeableConcept[];
  /**
   * For systems that need to know which was the primary diagnosis, these will be marked with the standard extension primaryDiagnosis (which is a sequence value rather than a flag, 1 = primary diagnosis).
   */
  reasonReference?: Reference[];
  /**
   * The organization that is primarily responsible for this Encounter's services. This MAY be the same as the organization on the Patient record, however it could be different, such as if the actor performing the services was from an external organization (which may be billed seperately) for an external consultation.  Refer to the example bundle showing an abbreviated set of Encounters for a colonoscopy.
   */
  serviceProvider?: Reference;
  /**
   * Broad categorization of the service that is to be provided (e.g. cardiology).
   */
  serviceType?: CodeableConcept;
  /**
   * Note that internal business rules will determine the appropriate transitions that may occur between statuses (and also classes).
   */
  status: string;
  _status?: Element;
  /**
   * The current status is always found in the current version of the resource, not the status history.
   */
  statusHistory?: EncounterStatusHistory[];
  /**
   * While the encounter is always about the patient, the patient might not actually be known in all contexts of use, and there may be a group of patients that could be anonymous (such as in a group therapy for Alcoholics Anonymous - where the recording of the encounter could be used for billing on the number of people/staff and not important to the context of the specific patients) or alternately in veterinary care a herd of sheep receiving treatment (where the animals are not individually tracked).
   */
  subject?: Reference;
  /**
   * Since there are many ways to further classify encounters, this element is 0..*.
   */
  type?: CodeableConcept[];
}
/**
 * Code Values for the Encounter.status field
 */
export enum EncounterStatusCodes {
  PLANNED = 'planned',
  ARRIVED = 'arrived',
  TRIAGED = 'triaged',
  IN_PROGRESS = 'in-progress',
  ONLEAVE = 'onleave',
  FINISHED = 'finished',
  CANCELLED = 'cancelled',
}
/**
 * The technical details of an endpoint that can be used for electronic services, such as for web services providing XDS.b or a REST endpoint for another FHIR server. This may include any security context information.
 */
export interface Endpoint extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Endpoint';
  /**
   * For rest-hook, and websocket, the end-point must be an http: or https: URL; for email, a mailto: url, for sms, a tel: url, and for message the endpoint can be in any form of url the server understands (usually, http: or mllp:). The URI is allowed to be relative; in which case, it is relative to the server end-point (since there may be more than one, clients should avoid using relative URIs)
   * This address will be to the service base, without any parameters, or sub-services or resources tacked on.
   * E.g. for a WADO-RS endpoint, the url should be "https://pacs.hospital.org/wado-rs"
   * and not "https://pacs.hospital.org/wado-rs/studies/1.2.250.1.59.40211.12345678.678910/series/1.2.250.1.59.40211.789001276.14556172.67789/instances/...".
   */
  address: string;
  _address?: Element;
  /**
   * For additional connectivity details for the protocol, extensions will be used at this point, as in the XDS example.
   */
  connectionType: Coding;
  /**
   * Contact details for a human to contact about the subscription. The primary use of this for system administrator troubleshooting.
   */
  contact?: ContactPoint[];
  /**
   * Exactly what these mean depends on the channel type. The can convey additional information to the recipient and/or meet security requirements.
   */
  header?: string[];
  _header?: Element;
  /**
   * Identifier for the organization that is used to identify the endpoint across multiple disparate systems.
   */
  identifier?: Identifier[];
  /**
   * This property is not typically used when searching for Endpoint resources for usage. The typical usage is via the reference from an applicable Organization/Location/Practitioner resource, which is where the context is provided. Multiple Locations may reference a single endpoint, and don't have to be within the same organization resource, but most likely within the same organizational hierarchy.
   */
  managingOrganization?: Reference;
  /**
   * A friendly name that this endpoint can be referred to with.
   */
  name?: string;
  _name?: Element;
  /**
   * Sending the payload has obvious security consequences. The server is responsible for ensuring that the content is appropriately secured.
   */
  payloadMimeType?: string[];
  _payloadMimeType?: Element;
  /**
   * The payloadFormat describes the serialization format of the data, where the payloadType indicates the specific document/schema that is being transferred; e.g. DischargeSummary or CarePlan.
   */
  payloadType: CodeableConcept[];
  /**
   * The interval during which the endpoint is expected to be operational.
   */
  period?: Period;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the endpoint as not currently valid.
   */
  status: string;
  _status?: Element;
}
/**
 * Code Values for the Endpoint.status field
 */
export enum EndpointStatusCodes {
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  ERROR = 'error',
  OFF = 'off',
  ENTERED_IN_ERROR = 'entered-in-error',
  TEST = 'test',
}
/**
 * This resource provides the insurance enrollment details to the insurer regarding a specified coverage.
 */
export interface EnrollmentRequest extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'EnrollmentRequest';
  /**
   * Patient Resource.
   */
  candidate?: Reference;
  /**
   * Reference to the program or plan identification, underwriter or payor.
   */
  coverage?: Reference;
  /**
   * The date when this resource was created.
   */
  created?: string;
  _created?: Element;
  /**
   * The Response business identifier.
   */
  identifier?: Identifier[];
  /**
   * The Insurer who is target  of the request.
   */
  insurer?: Reference;
  /**
   * The practitioner who is responsible for the services rendered to the patient.
   */
  provider?: Reference;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the request as not currently valid.
   */
  status?: string;
  _status?: Element;
}
/**
 * Code Values for the EnrollmentRequest.status field
 */
export enum EnrollmentRequestStatusCodes {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  DRAFT = 'draft',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * This resource provides enrollment and plan details from the processing of an EnrollmentRequest resource.
 */
export interface EnrollmentResponse extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'EnrollmentResponse';
  /**
   * The date when the enclosed suite of services were performed or completed.
   */
  created?: string;
  _created?: Element;
  /**
   * A description of the status of the adjudication.
   */
  disposition?: string;
  _disposition?: Element;
  /**
   * The Response business identifier.
   */
  identifier?: Identifier[];
  /**
   * The Insurer who produced this adjudicated response.
   */
  organization?: Reference;
  /**
   * Processing status: error, complete.
   */
  outcome?: string;
  _outcome?: Element;
  /**
   * Original request resource reference.
   */
  request?: Reference;
  /**
   * The practitioner who is responsible for the services rendered to the patient.
   */
  requestProvider?: Reference;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the response as not currently valid.
   */
  status?: string;
  _status?: Element;
}
/**
 * Code Values for the EnrollmentResponse.outcome field
 */
export enum EnrollmentResponseOutcomeCodes {
  QUEUED = 'queued',
  COMPLETE = 'complete',
  ERROR = 'error',
  PARTIAL = 'partial',
}
/**
 * Code Values for the EnrollmentResponse.status field
 */
export enum EnrollmentResponseStatusCodes {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  DRAFT = 'draft',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * The history of statuses that the EpisodeOfCare has been through (without requiring processing the history of the resource).
 */
export interface EpisodeOfCareStatusHistory extends BackboneElement {
  /**
   * The period during this EpisodeOfCare that the specific status applied.
   */
  period: Period;
  /**
   * planned | waitlist | active | onhold | finished | cancelled.
   */
  status: string;
  _status?: Element;
}
/**
 * Code Values for the EpisodeOfCare.statusHistory.status field
 */
export enum EpisodeOfCareStatusHistoryStatusCodes {
  PLANNED = 'planned',
  WAITLIST = 'waitlist',
  ACTIVE = 'active',
  ONHOLD = 'onhold',
  FINISHED = 'finished',
  CANCELLED = 'cancelled',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * The list of diagnosis relevant to this episode of care.
 */
export interface EpisodeOfCareDiagnosis extends BackboneElement {
  /**
   * A list of conditions/problems/diagnoses that this episode of care is intended to be providing care for.
   */
  condition: Reference;
  /**
   * Ranking of the diagnosis (for each role type).
   */
  rank?: number;
  /**
   * Role that this diagnosis has within the episode of care (e.g. admission, billing, discharge …).
   */
  role?: CodeableConcept;
}
/**
 * An association between a patient and an organization / healthcare provider(s) during which time encounters may occur. The managing organization assumes a level of responsibility for the patient during this time.
 */
export interface EpisodeOfCare extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'EpisodeOfCare';
  /**
   * The billing system may choose to allocate billable items associated with the EpisodeOfCare to different referenced Accounts based on internal business rules.
   */
  account?: Reference[];
  /**
   * The practitioner that is the care manager/care coordinator for this patient.
   */
  careManager?: Reference;
  /**
   * The list of diagnosis relevant to this episode of care.
   */
  diagnosis?: EpisodeOfCareDiagnosis[];
  /**
   * The EpisodeOfCare may be known by different identifiers for different contexts of use, such as when an external agency is tracking the Episode for funding purposes.
   */
  identifier?: Identifier[];
  /**
   * The organization that has assumed the specific responsibilities for the specified duration.
   */
  managingOrganization?: Reference;
  /**
   * The patient who is the focus of this episode of care.
   */
  patient: Reference;
  /**
   * The interval during which the managing organization assumes the defined responsibility.
   */
  period?: Period;
  /**
   * Referral Request(s) that are fulfilled by this EpisodeOfCare, incoming referrals.
   */
  referralRequest?: Reference[];
  /**
   * This element is labeled as a modifier because the status contains codes that mark the episode as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * The history of statuses that the EpisodeOfCare has been through (without requiring processing the history of the resource).
   */
  statusHistory?: EpisodeOfCareStatusHistory[];
  /**
   * The list of practitioners that may be facilitating this episode of care for specific purposes.
   */
  team?: Reference[];
  /**
   * The type can be very important in processing as this could be used in determining if the EpisodeOfCare is relevant to specific government reporting, or other types of classifications.
   */
  type?: CodeableConcept[];
}
/**
 * Code Values for the EpisodeOfCare.status field
 */
export enum EpisodeOfCareStatusCodes {
  PLANNED = 'planned',
  WAITLIST = 'waitlist',
  ACTIVE = 'active',
  ONHOLD = 'onhold',
  FINISHED = 'finished',
  CANCELLED = 'cancelled',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * The EventDefinition resource provides a reusable description of when a particular event can occur.
 */
export interface EventDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'EventDefinition';
  /**
   * The 'date' element may be more recent than the approval date because of minor changes or editorial corrections.
   */
  approvalDate?: string;
  _approvalDate?: Element;
  /**
   * An individiual or organization primarily involved in the creation and maintenance of the content.
   */
  author?: ContactDetail[];
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the event definition and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the event definition.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the event definition. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the event definition was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the event definition as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the event definition is presumed to be the predominant language in the place the event definition was created).
   */
  description?: string;
  _description?: Element;
  /**
   * An individual or organization primarily responsible for internal coherence of the content.
   */
  editor?: ContactDetail[];
  /**
   * The effective period for a event definition  determines when the content is applicable for usage and is independent of publication and review dates. For example, a measure intended to be used for the year 2016 might be published in 2015.
   */
  effectivePeriod?: Period;
  /**
   * An individual or organization responsible for officially endorsing the content for use in some setting.
   */
  endorser?: ContactDetail[];
  /**
   * Allows filtering of event definitions that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this event definition outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * It may be possible for the event definition to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * If specified, this date follows the original approval date.
   */
  lastReviewDate?: string;
  _lastReviewDate?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the event definition is the organization or individual primarily responsible for the maintenance and upkeep of the event definition. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the event definition. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the event definition. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this event definition.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Each related resource is either an attachment, or a reference to another resource, but not both.
   */
  relatedArtifact?: RelatedArtifact[];
  /**
   * An individual or organization primarily responsible for review of some aspect of the content.
   */
  reviewer?: ContactDetail[];
  /**
   * Allows filtering of event definitions that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * A code or group definition that describes the intended subject of the event definition.
   */
  subjectCodeableConcept?: CodeableConcept;
  /**
   * A code or group definition that describes the intended subject of the event definition.
   */
  subjectReference?: Reference;
  /**
   * An explanatory or alternate title for the event definition giving additional information about its content.
   */
  subtitle?: string;
  _subtitle?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Descriptive topics related to the module. Topics provide a high-level categorization of the module that can be useful for filtering and searching.
   */
  topic?: CodeableConcept[];
  /**
   * The trigger element defines when the event occurs. If more than one trigger condition is specified, the event fires whenever any one of the trigger conditions is met.
   */
  trigger: TriggerDefinition[];
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * A detailed description of how the event definition is used from a clinical perspective.
   */
  usage?: string;
  _usage?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different event definition instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the event definition with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the EventDefinition.status field
 */
export enum EventDefinitionStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * The Evidence resource describes the conditional state (population and any exposures being compared within the population) and outcome (if specified) that the knowledge (evidence, assertion, recommendation) is about.
 */
export interface Evidence extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Evidence';
  /**
   * The 'date' element may be more recent than the approval date because of minor changes or editorial corrections.
   */
  approvalDate?: string;
  _approvalDate?: Element;
  /**
   * An individiual or organization primarily involved in the creation and maintenance of the content.
   */
  author?: ContactDetail[];
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the evidence and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the evidence.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the evidence. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the evidence was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the evidence as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the evidence is presumed to be the predominant language in the place the evidence was created).
   */
  description?: string;
  _description?: Element;
  /**
   * An individual or organization primarily responsible for internal coherence of the content.
   */
  editor?: ContactDetail[];
  /**
   * The effective period for a evidence  determines when the content is applicable for usage and is independent of publication and review dates. For example, a measure intended to be used for the year 2016 might be published in 2015.
   */
  effectivePeriod?: Period;
  /**
   * An individual or organization responsible for officially endorsing the content for use in some setting.
   */
  endorser?: ContactDetail[];
  /**
   * A reference to a EvidenceVariable resource that defines the population for the research.
   */
  exposureBackground: Reference;
  /**
   * A reference to a EvidenceVariable resource that defines the exposure for the research.
   */
  exposureVariant?: Reference[];
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this evidence outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * It may be possible for the evidence to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * If specified, this date follows the original approval date.
   */
  lastReviewDate?: string;
  _lastReviewDate?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * A human-readable string to clarify or explain concepts about the resource.
   */
  note?: Annotation[];
  /**
   * A reference to a EvidenceVariable resomece that defines the outcome for the research.
   */
  outcome?: Reference[];
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the evidence is the organization or individual primarily responsible for the maintenance and upkeep of the evidence. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the evidence. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * Each related artifact is either an attachment, or a reference to another resource, but not both.
   */
  relatedArtifact?: RelatedArtifact[];
  /**
   * An individual or organization primarily responsible for review of some aspect of the content.
   */
  reviewer?: ContactDetail[];
  /**
   * The short title provides an alternate title for use in informal descriptive contexts where the full, formal title is not necessary.
   */
  shortTitle?: string;
  _shortTitle?: Element;
  /**
   * Allows filtering of evidences that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * An explanatory or alternate title for the Evidence giving additional information about its content.
   */
  subtitle?: string;
  _subtitle?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Descriptive topics related to the content of the Evidence. Topics provide a high-level categorization grouping types of Evidences that can be useful for filtering and searching.
   */
  topic?: CodeableConcept[];
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different evidence instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the evidence with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the Evidence.status field
 */
export enum EvidenceStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Characteristics can be defined flexibly to accommodate different use cases for membership criteria, ranging from simple codes, all the way to using an expression language to express the criteria.
 */
export interface EvidenceVariableCharacteristic extends BackboneElement {
  /**
   * Define members of the evidence element using Codes (such as condition, medication, or observation), Expressions ( using an expression language such as FHIRPath or CQL) or DataRequirements (such as Diabetes diagnosis onset in the last year).
   */
  definitionReference: Reference;
  /**
   * Define members of the evidence element using Codes (such as condition, medication, or observation), Expressions ( using an expression language such as FHIRPath or CQL) or DataRequirements (such as Diabetes diagnosis onset in the last year).
   */
  definitionCanonical: string;
  _definitionCanonical?: Element;
  /**
   * Define members of the evidence element using Codes (such as condition, medication, or observation), Expressions ( using an expression language such as FHIRPath or CQL) or DataRequirements (such as Diabetes diagnosis onset in the last year).
   */
  definitionCodeableConcept: CodeableConcept;
  /**
   * Define members of the evidence element using Codes (such as condition, medication, or observation), Expressions ( using an expression language such as FHIRPath or CQL) or DataRequirements (such as Diabetes diagnosis onset in the last year).
   */
  definitionExpression: Expression;
  /**
   * Define members of the evidence element using Codes (such as condition, medication, or observation), Expressions ( using an expression language such as FHIRPath or CQL) or DataRequirements (such as Diabetes diagnosis onset in the last year).
   */
  definitionDataRequirement: DataRequirement;
  /**
   * Define members of the evidence element using Codes (such as condition, medication, or observation), Expressions ( using an expression language such as FHIRPath or CQL) or DataRequirements (such as Diabetes diagnosis onset in the last year).
   */
  definitionTriggerDefinition: TriggerDefinition;
  /**
   * A short, natural language description of the characteristic that could be used to communicate the criteria to an end-user.
   */
  description?: string;
  _description?: Element;
  /**
   * When true, members with this characteristic are excluded from the element.
   */
  exclude?: boolean;
  _exclude?: Element;
  /**
   * Indicates how elements are aggregated within the study effective period.
   */
  groupMeasure?: string;
  _groupMeasure?: Element;
  /**
   * Indicates what effective period the study covers.
   */
  participantEffectiveDateTime?: string;
  _participantEffectiveDateTime?: Element;
  /**
   * Indicates what effective period the study covers.
   */
  participantEffectivePeriod?: Period;
  /**
   * Indicates what effective period the study covers.
   */
  participantEffectiveDuration?: Duration;
  /**
   * Indicates what effective period the study covers.
   */
  participantEffectiveTiming?: Timing;
  /**
   * Indicates duration from the participant's study entry.
   */
  timeFromStart?: Duration;
  /**
   * Use UsageContext to define the members of the population, such as Age Ranges, Genders, Settings.
   */
  usageContext?: UsageContext[];
}
/**
 * Code Values for the EvidenceVariable.characteristic.groupMeasure field
 */
export enum EvidenceVariableCharacteristicGroupMeasureCodes {
  MEAN = 'mean',
  MEDIAN = 'median',
  MEAN_OF_MEAN = 'mean-of-mean',
  MEAN_OF_MEDIAN = 'mean-of-median',
  MEDIAN_OF_MEAN = 'median-of-mean',
  MEDIAN_OF_MEDIAN = 'median-of-median',
}
/**
 * The EvidenceVariable resource describes a "PICO" element that knowledge (evidence, assertion, recommendation) is about.
 */
export interface EvidenceVariable extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'EvidenceVariable';
  /**
   * The 'date' element may be more recent than the approval date because of minor changes or editorial corrections.
   */
  approvalDate?: string;
  _approvalDate?: Element;
  /**
   * An individiual or organization primarily involved in the creation and maintenance of the content.
   */
  author?: ContactDetail[];
  /**
   * Characteristics can be defined flexibly to accommodate different use cases for membership criteria, ranging from simple codes, all the way to using an expression language to express the criteria.
   */
  characteristic: EvidenceVariableCharacteristic[];
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the evidence variable and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the evidence variable.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the evidence variable. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the evidence variable was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the evidence variable as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the evidence variable is presumed to be the predominant language in the place the evidence variable was created).
   */
  description?: string;
  _description?: Element;
  /**
   * An individual or organization primarily responsible for internal coherence of the content.
   */
  editor?: ContactDetail[];
  /**
   * The effective period for a evidence variable  determines when the content is applicable for usage and is independent of publication and review dates. For example, a measure intended to be used for the year 2016 might be published in 2015.
   */
  effectivePeriod?: Period;
  /**
   * An individual or organization responsible for officially endorsing the content for use in some setting.
   */
  endorser?: ContactDetail[];
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this evidence variable outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * It may be possible for the evidence variable to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * If specified, this date follows the original approval date.
   */
  lastReviewDate?: string;
  _lastReviewDate?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * A human-readable string to clarify or explain concepts about the resource.
   */
  note?: Annotation[];
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the evidence variable is the organization or individual primarily responsible for the maintenance and upkeep of the evidence variable. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the evidence variable. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * Each related artifact is either an attachment, or a reference to another resource, but not both.
   */
  relatedArtifact?: RelatedArtifact[];
  /**
   * An individual or organization primarily responsible for review of some aspect of the content.
   */
  reviewer?: ContactDetail[];
  /**
   * The short title provides an alternate title for use in informal descriptive contexts where the full, formal title is not necessary.
   */
  shortTitle?: string;
  _shortTitle?: Element;
  /**
   * Allows filtering of evidence variables that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * An explanatory or alternate title for the EvidenceVariable giving additional information about its content.
   */
  subtitle?: string;
  _subtitle?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Descriptive topics related to the content of the EvidenceVariable. Topics provide a high-level categorization grouping types of EvidenceVariables that can be useful for filtering and searching.
   */
  topic?: CodeableConcept[];
  /**
   * The type of evidence element, a population, an exposure, or an outcome.
   */
  type?: string;
  _type?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different evidence variable instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the evidence variable with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the EvidenceVariable.status field
 */
export enum EvidenceVariableStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Code Values for the EvidenceVariable.type field
 */
export enum EvidenceVariableTypeCodes {
  DICHOTOMOUS = 'dichotomous',
  CONTINUOUS = 'continuous',
  DESCRIPTIVE = 'descriptive',
}
/**
 * Actor participating in the resource.
 */
export interface ExampleScenarioActor extends BackboneElement {
  /**
   * should this be called ID or acronym?
   */
  actorId: string;
  _actorId?: Element;
  /**
   * Cardinality: is name and description 1..1?
   */
  description?: string;
  _description?: Element;
  /**
   * Cardinality: is name and description 1..1?
   */
  name?: string;
  _name?: Element;
  /**
   * The type of actor - person or system.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the ExampleScenario.actor.type field
 */
export enum ExampleScenarioActorTypeCodes {
  PERSON = 'person',
  ENTITY = 'entity',
}
/**
 * A specific version of the resource.
 */
export interface ExampleScenarioInstanceVersion extends BackboneElement {
  /**
   * The description of the resource version.
   */
  description: string;
  _description?: Element;
  /**
   * The identifier of a specific version of a resource.
   */
  versionId: string;
  _versionId?: Element;
}
/**
 * Resources contained in the instance (e.g. the observations contained in a bundle).
 */
export interface ExampleScenarioInstanceContainedInstance extends BackboneElement {
  /**
   * Each resource contained in the instance.
   */
  resourceId: string;
  _resourceId?: Element;
  /**
   * A specific version of a resource contained in the instance.
   */
  versionId?: string;
  _versionId?: Element;
}
/**
 * Each resource and each version that is present in the workflow.
 */
export interface ExampleScenarioInstance extends BackboneElement {
  /**
   * Resources contained in the instance (e.g. the observations contained in a bundle).
   */
  containedInstance?: ExampleScenarioInstanceContainedInstance[];
  /**
   * Human-friendly description of the resource instance.
   */
  description?: string;
  _description?: Element;
  /**
   * A short name for the resource instance.
   */
  name?: string;
  _name?: Element;
  /**
   * The id of the resource for referencing.
   */
  resourceId: string;
  _resourceId?: Element;
  /**
   * The type of the resource.
   */
  resourceType: string;
  _resourceType?: Element;
  /**
   * A specific version of the resource.
   */
  version?: ExampleScenarioInstanceVersion[];
}
/**
 * Each interaction or action.
 */
export interface ExampleScenarioProcessStepOperation extends BackboneElement {
  /**
   * A comment to be inserted in the diagram.
   */
  description?: string;
  _description?: Element;
  /**
   * Who starts the transaction.
   */
  initiator?: string;
  _initiator?: Element;
  /**
   * Whether the initiator is deactivated right after the transaction.
   */
  initiatorActive?: boolean;
  _initiatorActive?: Element;
  /**
   * The human-friendly name of the interaction.
   */
  name?: string;
  _name?: Element;
  /**
   * The sequential number of the interaction, e.g. 1.2.5.
   */
  number: string;
  _number?: Element;
  /**
   * Who receives the transaction.
   */
  receiver?: string;
  _receiver?: Element;
  /**
   * Whether the receiver is deactivated right after the transaction.
   */
  receiverActive?: boolean;
  _receiverActive?: Element;
  /**
   * Each resource instance used by the initiator.
   */
  request?: ExampleScenarioInstanceContainedInstance;
  /**
   * Each resource instance used by the responder.
   */
  response?: ExampleScenarioInstanceContainedInstance;
  /**
   * The type of operation - CRUD.
   */
  type?: string;
  _type?: Element;
}
/**
 * Indicates an alternative step that can be taken instead of the operations on the base step in exceptional/atypical circumstances.
 */
export interface ExampleScenarioProcessStepAlternative extends BackboneElement {
  /**
   * A human-readable description of the alternative explaining when the alternative should occur rather than the base step.
   */
  description?: string;
  _description?: Element;
  /**
   * What happens in each alternative option.
   */
  step?: ExampleScenarioProcessStep[];
  /**
   * The label to display for the alternative that gives a sense of the circumstance in which the alternative should be invoked.
   */
  title: string;
  _title?: Element;
}
/**
 * Each step of the process.
 */
export interface ExampleScenarioProcessStep extends BackboneElement {
  /**
   * Indicates an alternative step that can be taken instead of the operations on the base step in exceptional/atypical circumstances.
   */
  alternative?: ExampleScenarioProcessStepAlternative[];
  /**
   * Each interaction or action.
   */
  operation?: ExampleScenarioProcessStepOperation;
  /**
   * If there is a pause in the flow.
   */
  pause?: boolean;
  _pause?: Element;
  /**
   * Nested process.
   */
  process?: ExampleScenarioProcess[];
}
/**
 * Each major process - a group of operations.
 */
export interface ExampleScenarioProcess extends BackboneElement {
  /**
   * A longer description of the group of operations.
   */
  description?: string;
  _description?: Element;
  /**
   * Description of final status after the process ends.
   */
  postConditions?: string;
  _postConditions?: Element;
  /**
   * Description of initial status before the process starts.
   */
  preConditions?: string;
  _preConditions?: Element;
  /**
   * Each step of the process.
   */
  step?: ExampleScenarioProcessStep[];
  /**
   * The diagram title of the group of operations.
   */
  title: string;
  _title?: Element;
}
/**
 * Example of workflow instance.
 */
export interface ExampleScenario extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ExampleScenario';
  /**
   * Actor participating in the resource.
   */
  actor?: ExampleScenarioActor[];
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * nullFrequently, the copyright differs between the value set and the codes that are included. The copyright statement should clearly differentiate between these when required.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the example scenario. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * Allows filtering of example scenarios that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this example scenario outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * Each resource and each version that is present in the workflow.
   */
  instance?: ExampleScenarioInstance[];
  /**
   * It may be possible for the example scenario to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * Each major process - a group of operations.
   */
  process?: ExampleScenarioProcess[];
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the example scenario is the organization or individual primarily responsible for the maintenance and upkeep of the example scenario. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the example scenario. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the example scenario. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this example scenario.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Allows filtering of example scenarios that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different example scenario instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the example scenario with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
  /**
   * Another nested workflow.
   */
  workflow?: string[];
  _workflow?: Element;
}
/**
 * Code Values for the ExampleScenario.status field
 */
export enum ExampleScenarioStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * For example,  for the original treatment and follow-up exams.
 */
export interface ExplanationOfBenefitRelated extends BackboneElement {
  /**
   * Reference to a related claim.
   */
  claim?: Reference;
  /**
   * For example, Property/Casualty insurer claim number or Workers Compensation case number.
   */
  reference?: Identifier;
  /**
   * For example, prior claim or umbrella.
   */
  relationship?: CodeableConcept;
}
/**
 * Often providers agree to receive the benefits payable to reduce the near-term costs to the patient. The insurer may decline to pay the provider and may choose to pay the subscriber instead.
 */
export interface ExplanationOfBenefitPayee extends BackboneElement {
  /**
   * Not required if the payee is 'subscriber' or 'provider'.
   */
  party?: Reference;
  /**
   * Type of Party to be reimbursed: Subscriber, provider, other.
   */
  type?: CodeableConcept;
}
/**
 * The members of the team who provided the products and services.
 */
export interface ExplanationOfBenefitCareTeam extends BackboneElement {
  /**
   * Member of the team who provided the product or service.
   */
  provider: Reference;
  /**
   * The qualification of the practitioner which is applicable for this service.
   */
  qualification?: CodeableConcept;
  /**
   * Responsible might not be required when there is only a single provider listed.
   */
  responsible?: boolean;
  _responsible?: Element;
  /**
   * Role might not be required when there is only a single provider listed.
   */
  role?: CodeableConcept;
  /**
   * A number to uniquely identify care team entries.
   */
  sequence: number;
}
/**
 * Often there are multiple jurisdiction specific valuesets which are required.
 */
export interface ExplanationOfBenefitSupportingInfo extends BackboneElement {
  /**
   * This may contain a category for the local bill type codes.
   */
  category: CodeableConcept;
  /**
   * This may contain the local bill type codes such as the US UB-04 bill type code.
   */
  code?: CodeableConcept;
  /**
   * For example: the reason for the additional stay, or why a tooth is  missing.
   */
  reason?: Coding;
  /**
   * A number to uniquely identify supporting information entries.
   */
  sequence: number;
  /**
   * The date when or period to which this information refers.
   */
  timingDate?: string;
  _timingDate?: Element;
  /**
   * The date when or period to which this information refers.
   */
  timingPeriod?: Period;
  /**
   * Could be used to provide references to other resources, document. For example, could contain a PDF in an Attachment of the Police Report for an Accident.
   */
  valueBoolean?: boolean;
  _valueBoolean?: Element;
  /**
   * Could be used to provide references to other resources, document. For example, could contain a PDF in an Attachment of the Police Report for an Accident.
   */
  valueString?: string;
  _valueString?: Element;
  /**
   * Could be used to provide references to other resources, document. For example, could contain a PDF in an Attachment of the Police Report for an Accident.
   */
  valueQuantity?: Quantity;
  /**
   * Could be used to provide references to other resources, document. For example, could contain a PDF in an Attachment of the Police Report for an Accident.
   */
  valueAttachment?: Attachment;
  /**
   * Could be used to provide references to other resources, document. For example, could contain a PDF in an Attachment of the Police Report for an Accident.
   */
  valueReference?: Reference;
}
/**
 * Information about diagnoses relevant to the claim items.
 */
export interface ExplanationOfBenefitDiagnosis extends BackboneElement {
  /**
   * The nature of illness or problem in a coded form or as a reference to an external defined Condition.
   */
  diagnosisCodeableConcept: CodeableConcept;
  /**
   * The nature of illness or problem in a coded form or as a reference to an external defined Condition.
   */
  diagnosisReference: Reference;
  /**
   * Indication of whether the diagnosis was present on admission to a facility.
   */
  onAdmission?: CodeableConcept;
  /**
   * For example, DRG (Diagnosis Related Group) or a bundled billing code. A patient may have a diagnosis of a Myocardio-infarction and a DRG for HeartAttack would assigned. The Claim item (and possible subsequent claims) would refer to the DRG for those line items that were for services related to the heart attack event.
   */
  packageCode?: CodeableConcept;
  /**
   * Diagnosis are presented in list order to their expected importance: primary, secondary, etc.
   */
  sequence: number;
  /**
   * For example: admitting, primary, secondary, discharge.
   */
  type?: CodeableConcept[];
}
/**
 * Procedures performed on the patient relevant to the billing items with the claim.
 */
export interface ExplanationOfBenefitProcedure extends BackboneElement {
  /**
   * Date and optionally time the procedure was performed.
   */
  date?: string;
  _date?: Element;
  /**
   * The code or reference to a Procedure resource which identifies the clinical intervention performed.
   */
  procedureCodeableConcept: CodeableConcept;
  /**
   * The code or reference to a Procedure resource which identifies the clinical intervention performed.
   */
  procedureReference: Reference;
  /**
   * A number to uniquely identify procedure entries.
   */
  sequence: number;
  /**
   * When the condition was observed or the relative ranking.
   */
  type?: CodeableConcept[];
  /**
   * Unique Device Identifiers associated with this line item.
   */
  udi?: Reference[];
}
/**
 * All insurance coverages for the patient which may be applicable for reimbursement, of the products and services listed in the claim, are typically provided in the claim to allow insurers to confirm the ordering of the insurance coverages relative to local 'coordination of benefit' rules. One coverage (and only one) with 'focal=true' is to be used in the adjudication of this claim. Coverages appearing before the focal Coverage in the list, and where 'Coverage.subrogation=false', should provide a reference to the ClaimResponse containing the adjudication results of the prior claim.
 */
export interface ExplanationOfBenefitInsurance extends BackboneElement {
  /**
   * Reference to the insurance card level information contained in the Coverage resource. The coverage issuing insurer will use these details to locate the patient's actual coverage within the insurer's information system.
   */
  coverage: Reference;
  /**
   * A patient may (will) have multiple insurance policies which provide reimbursement for healthcare services and products. For example, a person may also be covered by their spouse's policy and both appear in the list (and may be from the same insurer). This flag will be set to true for only one of the listed policies and that policy will be used for adjudicating this claim. Other claims would be created to request adjudication against the other listed policies.
   */
  focal: boolean;
  _focal?: Element;
  /**
   * This value is an alphanumeric string that may be provided over the phone, via text, via paper, or within a ClaimResponse resource and is not a FHIR Identifier.
   */
  preAuthRef?: string[];
  _preAuthRef?: Element;
}
/**
 * Details of a accident which resulted in injuries which required the products and services listed in the claim.
 */
export interface ExplanationOfBenefitAccident extends BackboneElement {
  /**
   * The date of the accident has to precede the dates of the products and services but within a reasonable timeframe.
   */
  date?: string;
  _date?: Element;
  /**
   * The physical location of the accident event.
   */
  locationAddress?: Address;
  /**
   * The physical location of the accident event.
   */
  locationReference?: Reference;
  /**
   * The type or context of the accident event for the purposes of selection of potential insurance coverages and determination of coordination between insurers.
   */
  type?: CodeableConcept;
}
/**
 * If this item is a group then the values here are a summary of the adjudication of the detail items. If this item is a simple product or service then this is the result of the adjudication of this item.
 */
export interface ExplanationOfBenefitItemAdjudication extends BackboneElement {
  /**
   * For example, amount submitted, eligible amount, co-payment, and benefit payable.
   */
  amount?: Money;
  /**
   * For example, codes indicating: Co-Pay, deductible, eligible, benefit, tax, etc.
   */
  category: CodeableConcept;
  /**
   * For example, may indicate that the funds for this benefit type have been exhausted.
   */
  reason?: CodeableConcept;
  /**
   * For example: eligible percentage or co-payment percentage.
   */
  value?: number;
}
/**
 * Third-tier of goods and services.
 */
export interface ExplanationOfBenefitItemDetailSubDetail extends BackboneElement {
  /**
   * The adjudication results.
   */
  adjudication?: ExplanationOfBenefitItemAdjudication[];
  /**
   * Examples include Medical Care, Periodontics, Renal Dialysis, Vision Coverage.
   */
  category?: CodeableConcept;
  /**
   * To show a 10% senior's discount, the value entered is: 0.90 (1.00 - 0.10).
   */
  factor?: number;
  /**
   * For example, in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or outside of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * For example, the formula: quantity * unitPrice * factor  = net. Quantity and factor are assumed to be 1 if not supplied.
   */
  net?: Money;
  /**
   * The numbers associated with notes below which apply to the adjudication of this item.
   */
  noteNumber?: number[];
  /**
   * If this is an actual service or product line, i.e. not a Group, then use code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI). If a grouping item then use a group code to indicate the type of thing being grouped e.g. 'glasses' or 'compound'.
   */
  productOrService: CodeableConcept;
  /**
   * For example: Neonatal program, child dental program or drug users recovery program.
   */
  programCode?: CodeableConcept[];
  /**
   * The number of repetitions of a service or product.
   */
  quantity?: Quantity;
  /**
   * The type of revenue or cost center providing the product and/or service.
   */
  revenue?: CodeableConcept;
  /**
   * A claim detail line. Either a simple (a product or service) or a 'group' of sub-details which are simple items.
   */
  sequence: number;
  /**
   * Unique Device Identifiers associated with this line item.
   */
  udi?: Reference[];
  /**
   * If the item is not a group then this is the fee for the product or service, otherwise this is the total of the fees for the details of the group.
   */
  unitPrice?: Money;
}
/**
 * Second-tier of goods and services.
 */
export interface ExplanationOfBenefitItemDetail extends BackboneElement {
  /**
   * The adjudication results.
   */
  adjudication?: ExplanationOfBenefitItemAdjudication[];
  /**
   * Examples include: Medical Care, Periodontics, Renal Dialysis, Vision Coverage.
   */
  category?: CodeableConcept;
  /**
   * To show a 10% senior's discount, the value entered is: 0.90 (1.00 - 0.10).
   */
  factor?: number;
  /**
   * For example, in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or out of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * For example, the formula: quantity * unitPrice * factor  = net. Quantity and factor are assumed to be 1 if not supplied.
   */
  net?: Money;
  /**
   * The numbers associated with notes below which apply to the adjudication of this item.
   */
  noteNumber?: number[];
  /**
   * If this is an actual service or product line, i.e. not a Group, then use code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI). If a grouping item then use a group code to indicate the type of thing being grouped e.g. 'glasses' or 'compound'.
   */
  productOrService: CodeableConcept;
  /**
   * For example: Neonatal program, child dental program or drug users recovery program.
   */
  programCode?: CodeableConcept[];
  /**
   * The number of repetitions of a service or product.
   */
  quantity?: Quantity;
  /**
   * The type of revenue or cost center providing the product and/or service.
   */
  revenue?: CodeableConcept;
  /**
   * A claim detail line. Either a simple (a product or service) or a 'group' of sub-details which are simple items.
   */
  sequence: number;
  /**
   * Third-tier of goods and services.
   */
  subDetail?: ExplanationOfBenefitItemDetailSubDetail[];
  /**
   * Unique Device Identifiers associated with this line item.
   */
  udi?: Reference[];
  /**
   * If the item is not a group then this is the fee for the product or service, otherwise this is the total of the fees for the details of the group.
   */
  unitPrice?: Money;
}
/**
 * A claim line. Either a simple (a product or service) or a 'group' of details which can also be a simple items or groups of sub-details.
 */
export interface ExplanationOfBenefitItem extends BackboneElement {
  /**
   * If this item is a group then the values here are a summary of the adjudication of the detail items. If this item is a simple product or service then this is the result of the adjudication of this item.
   */
  adjudication?: ExplanationOfBenefitItemAdjudication[];
  /**
   * For example: Providing a tooth code, allows an insurer to identify a provider performing a filling on a tooth that was previously removed.
   */
  bodySite?: CodeableConcept;
  /**
   * Care team members related to this service or product.
   */
  careTeamSequence?: number[];
  /**
   * Examples include Medical Care, Periodontics, Renal Dialysis, Vision Coverage.
   */
  category?: CodeableConcept;
  /**
   * Second-tier of goods and services.
   */
  detail?: ExplanationOfBenefitItemDetail[];
  /**
   * Diagnoses applicable for this service or product.
   */
  diagnosisSequence?: number[];
  /**
   * A billed item may include goods or services provided in multiple encounters.
   */
  encounter?: Reference[];
  /**
   * To show a 10% senior's discount, the value entered is: 0.90 (1.00 - 0.10).
   */
  factor?: number;
  /**
   * Exceptions, special conditions and supporting information applicable for this service or product.
   */
  informationSequence?: number[];
  /**
   * Where the product or service was provided.
   */
  locationCodeableConcept?: CodeableConcept;
  /**
   * Where the product or service was provided.
   */
  locationAddress?: Address;
  /**
   * Where the product or service was provided.
   */
  locationReference?: Reference;
  /**
   * For example, in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or out of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * For example, the formula: quantity * unitPrice * factor  = net. Quantity and factor are assumed to be 1 if not supplied.
   */
  net?: Money;
  /**
   * The numbers associated with notes below which apply to the adjudication of this item.
   */
  noteNumber?: number[];
  /**
   * Procedures applicable for this service or product.
   */
  procedureSequence?: number[];
  /**
   * If this is an actual service or product line, i.e. not a Group, then use code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI). If a grouping item then use a group code to indicate the type of thing being grouped e.g. 'glasses' or 'compound'.
   */
  productOrService: CodeableConcept;
  /**
   * For example: Neonatal program, child dental program or drug users recovery program.
   */
  programCode?: CodeableConcept[];
  /**
   * The number of repetitions of a service or product.
   */
  quantity?: Quantity;
  /**
   * The type of revenue or cost center providing the product and/or service.
   */
  revenue?: CodeableConcept;
  /**
   * A number to uniquely identify item entries.
   */
  sequence: number;
  /**
   * The date or dates when the service or product was supplied, performed or completed.
   */
  servicedDate?: string;
  _servicedDate?: Element;
  /**
   * The date or dates when the service or product was supplied, performed or completed.
   */
  servicedPeriod?: Period;
  /**
   * A region or surface of the bodySite, e.g. limb region or tooth surface(s).
   */
  subSite?: CodeableConcept[];
  /**
   * Unique Device Identifiers associated with this line item.
   */
  udi?: Reference[];
  /**
   * If the item is not a group then this is the fee for the product or service, otherwise this is the total of the fees for the details of the group.
   */
  unitPrice?: Money;
}
/**
 * The third-tier service adjudications for payor added services.
 */
export interface ExplanationOfBenefitAddItemDetailSubDetail extends BackboneElement {
  /**
   * The adjudication results.
   */
  adjudication?: ExplanationOfBenefitItemAdjudication[];
  /**
   * To show a 10% senior's discount, the value entered is: 0.90 (1.00 - 0.10).
   */
  factor?: number;
  /**
   * For example, in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or out of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * For example, the formula: quantity * unitPrice * factor  = net. Quantity and factor are assumed to be 1 if not supplied.
   */
  net?: Money;
  /**
   * The numbers associated with notes below which apply to the adjudication of this item.
   */
  noteNumber?: number[];
  /**
   * If this is an actual service or product line, i.e. not a Group, then use code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI). If a grouping item then use a group code to indicate the type of thing being grouped e.g. 'glasses' or 'compound'.
   */
  productOrService: CodeableConcept;
  /**
   * The number of repetitions of a service or product.
   */
  quantity?: Quantity;
  /**
   * If the item is not a group then this is the fee for the product or service, otherwise this is the total of the fees for the details of the group.
   */
  unitPrice?: Money;
}
/**
 * The second-tier service adjudications for payor added services.
 */
export interface ExplanationOfBenefitAddItemDetail extends BackboneElement {
  /**
   * The adjudication results.
   */
  adjudication?: ExplanationOfBenefitItemAdjudication[];
  /**
   * To show a 10% senior's discount, the value entered is: 0.90 (1.00 - 0.10).
   */
  factor?: number;
  /**
   * For example, in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or out of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * For example, the formula: quantity * unitPrice * factor  = net. Quantity and factor are assumed to be 1 if not supplied.
   */
  net?: Money;
  /**
   * The numbers associated with notes below which apply to the adjudication of this item.
   */
  noteNumber?: number[];
  /**
   * If this is an actual service or product line, i.e. not a Group, then use code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI). If a grouping item then use a group code to indicate the type of thing being grouped e.g. 'glasses' or 'compound'.
   */
  productOrService: CodeableConcept;
  /**
   * The number of repetitions of a service or product.
   */
  quantity?: Quantity;
  /**
   * The third-tier service adjudications for payor added services.
   */
  subDetail?: ExplanationOfBenefitAddItemDetailSubDetail[];
  /**
   * If the item is not a group then this is the fee for the product or service, otherwise this is the total of the fees for the details of the group.
   */
  unitPrice?: Money;
}
/**
 * The first-tier service adjudications for payor added product or service lines.
 */
export interface ExplanationOfBenefitAddItem extends BackboneElement {
  /**
   * The adjudication results.
   */
  adjudication?: ExplanationOfBenefitItemAdjudication[];
  /**
   * For example, providing a tooth code allows an insurer to identify a provider performing a filling on a tooth that was previously removed.
   */
  bodySite?: CodeableConcept;
  /**
   * The second-tier service adjudications for payor added services.
   */
  detail?: ExplanationOfBenefitAddItemDetail[];
  /**
   * The sequence number of the details within the claim item which this line is intended to replace.
   */
  detailSequence?: number[];
  /**
   * To show a 10% senior's discount, the value entered is: 0.90 (1.00 - 0.10).
   */
  factor?: number;
  /**
   * Claim items which this service line is intended to replace.
   */
  itemSequence?: number[];
  /**
   * Where the product or service was provided.
   */
  locationCodeableConcept?: CodeableConcept;
  /**
   * Where the product or service was provided.
   */
  locationAddress?: Address;
  /**
   * Where the product or service was provided.
   */
  locationReference?: Reference;
  /**
   * For example, in Oral whether the treatment is cosmetic or associated with TMJ, or for Medical whether the treatment was outside the clinic or out of office hours.
   */
  modifier?: CodeableConcept[];
  /**
   * For example, the formula: quantity * unitPrice * factor  = net. Quantity and factor are assumed to be 1 if not supplied.
   */
  net?: Money;
  /**
   * The numbers associated with notes below which apply to the adjudication of this item.
   */
  noteNumber?: number[];
  /**
   * If this is an actual service or product line, i.e. not a Group, then use code to indicate the Professional Service or Product supplied (e.g. CTP, HCPCS, USCLS, ICD10, NCPDP, DIN, RxNorm, ACHI, CCI). If a grouping item then use a group code to indicate the type of thing being grouped e.g. 'glasses' or 'compound'.
   */
  productOrService: CodeableConcept;
  /**
   * For example: Neonatal program, child dental program or drug users recovery program.
   */
  programCode?: CodeableConcept[];
  /**
   * The providers who are authorized for the services rendered to the patient.
   */
  provider?: Reference[];
  /**
   * The number of repetitions of a service or product.
   */
  quantity?: Quantity;
  /**
   * The date or dates when the service or product was supplied, performed or completed.
   */
  servicedDate?: string;
  _servicedDate?: Element;
  /**
   * The date or dates when the service or product was supplied, performed or completed.
   */
  servicedPeriod?: Period;
  /**
   * The sequence number of the sub-details woithin the details within the claim item which this line is intended to replace.
   */
  subDetailSequence?: number[];
  /**
   * A region or surface of the bodySite, e.g. limb region or tooth surface(s).
   */
  subSite?: CodeableConcept[];
  /**
   * If the item is not a group then this is the fee for the product or service, otherwise this is the total of the fees for the details of the group.
   */
  unitPrice?: Money;
}
/**
 * Totals for amounts submitted, co-pays, benefits payable etc.
 */
export interface ExplanationOfBenefitTotal extends BackboneElement {
  /**
   * Monetary total amount associated with the category.
   */
  amount: Money;
  /**
   * For example, codes indicating: Co-Pay, deductible, eligible, benefit, tax, etc.
   */
  category: CodeableConcept;
}
/**
 * Payment details for the adjudication of the claim.
 */
export interface ExplanationOfBenefitPayment extends BackboneElement {
  /**
   * Insurers will deduct amounts owing from the provider (adjustment), such as a prior overpayment, from the amount owing to the provider (benefits payable) when payment is made to the provider.
   */
  adjustment?: Money;
  /**
   * Reason for the payment adjustment.
   */
  adjustmentReason?: CodeableConcept;
  /**
   * Benefits payable less any payment adjustment.
   */
  amount?: Money;
  /**
   * Estimated date the payment will be issued or the actual issue date of payment.
   */
  date?: string;
  _date?: Element;
  /**
   * For example: EFT number or check number.
   */
  identifier?: Identifier;
  /**
   * Whether this represents partial or complete payment of the benefits payable.
   */
  type?: CodeableConcept;
}
/**
 * A note that describes or explains adjudication results in a human readable form.
 */
export interface ExplanationOfBenefitProcessNote extends BackboneElement {
  /**
   * Only required if the language is different from the resource language.
   */
  language?: CodeableConcept;
  /**
   * A number to uniquely identify a note entry.
   */
  number?: number;
  /**
   * The explanation or description associated with the processing.
   */
  text?: string;
  _text?: Element;
  /**
   * The business purpose of the note text.
   */
  type?: string;
  _type?: Element;
}
/**
 * Code Values for the ExplanationOfBenefit.processNote.type field
 */
export enum ExplanationOfBenefitProcessNoteTypeCodes {
  DISPLAY = 'display',
  PRINT = 'print',
  PRINTOPER = 'printoper',
}
/**
 * Benefits Used to date.
 */
export interface ExplanationOfBenefitBenefitBalanceFinancial extends BackboneElement {
  /**
   * The quantity of the benefit which is permitted under the coverage.
   */
  allowedUnsignedInt?: number;
  /**
   * The quantity of the benefit which is permitted under the coverage.
   */
  allowedString?: string;
  _allowedString?: Element;
  /**
   * The quantity of the benefit which is permitted under the coverage.
   */
  allowedMoney?: Money;
  /**
   * For example: deductible, visits, benefit amount.
   */
  type: CodeableConcept;
  /**
   * The quantity of the benefit which have been consumed to date.
   */
  usedUnsignedInt?: number;
  /**
   * The quantity of the benefit which have been consumed to date.
   */
  usedMoney?: Money;
}
/**
 * Balance by Benefit Category.
 */
export interface ExplanationOfBenefitBenefitBalance extends BackboneElement {
  /**
   * Examples include Medical Care, Periodontics, Renal Dialysis, Vision Coverage.
   */
  category: CodeableConcept;
  /**
   * For example, 'DENT2 covers 100% of basic, 50% of major but excludes Ortho, Implants and Cosmetic services'.
   */
  description?: string;
  _description?: Element;
  /**
   * True if the indicated class of service is excluded from the plan, missing or False indicates the product or service is included in the coverage.
   */
  excluded?: boolean;
  _excluded?: Element;
  /**
   * Benefits Used to date.
   */
  financial?: ExplanationOfBenefitBenefitBalanceFinancial[];
  /**
   * For example: MED01, or DENT2.
   */
  name?: string;
  _name?: Element;
  /**
   * Is a flag to indicate whether the benefits refer to in-network providers or out-of-network providers.
   */
  network?: CodeableConcept;
  /**
   * The term or period of the values such as 'maximum lifetime benefit' or 'maximum annual visits'.
   */
  term?: CodeableConcept;
  /**
   * Indicates if the benefits apply to an individual or to the family.
   */
  unit?: CodeableConcept;
}
/**
 * This resource provides: the claim details; adjudication details from the processing of a Claim; and optionally account balance information, for informing the subscriber of the benefits provided.
 */
export interface ExplanationOfBenefit extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ExplanationOfBenefit';
  /**
   * Details of a accident which resulted in injuries which required the products and services listed in the claim.
   */
  accident?: ExplanationOfBenefitAccident;
  /**
   * The first-tier service adjudications for payor added product or service lines.
   */
  addItem?: ExplanationOfBenefitAddItem[];
  /**
   * The adjudication results which are presented at the header level rather than at the line-item or add-item levels.
   */
  adjudication?: ExplanationOfBenefitItemAdjudication[];
  /**
   * Balance by Benefit Category.
   */
  benefitBalance?: ExplanationOfBenefitBenefitBalance[];
  /**
   * Not applicable when use=claim.
   */
  benefitPeriod?: Period;
  /**
   * Typically this would be today or in the past for a claim, and today or in the future for preauthorizations and prodeterminations. Typically line item dates of service should fall within the billing period if one is specified.
   */
  billablePeriod?: Period;
  /**
   * The members of the team who provided the products and services.
   */
  careTeam?: ExplanationOfBenefitCareTeam[];
  /**
   * The business identifier for the instance of the adjudication request: claim predetermination or preauthorization.
   */
  claim?: Reference;
  /**
   * The business identifier for the instance of the adjudication response: claim, predetermination or preauthorization response.
   */
  claimResponse?: Reference;
  /**
   * This field is independent of the date of creation of the resource as it may reflect the creation date of a source document prior to digitization. Typically for claims all services must be completed as of this date.
   */
  created: string;
  _created?: Element;
  /**
   * Information about diagnoses relevant to the claim items.
   */
  diagnosis?: ExplanationOfBenefitDiagnosis[];
  /**
   * A human readable description of the status of the adjudication.
   */
  disposition?: string;
  _disposition?: Element;
  /**
   * Individual who created the claim, predetermination or preauthorization.
   */
  enterer?: Reference;
  /**
   * Facility where the services were provided.
   */
  facility?: Reference;
  /**
   * Needed to permit insurers to include the actual form.
   */
  form?: Attachment;
  /**
   * May be needed to identify specific jurisdictional forms.
   */
  formCode?: CodeableConcept;
  /**
   * Fund would be release by a future claim quoting the preAuthRef of this response. Examples of values include: provider, patient, none.
   */
  fundsReserve?: CodeableConcept;
  /**
   * This field is only used for preauthorizations.
   */
  fundsReserveRequested?: CodeableConcept;
  /**
   * A unique identifier assigned to this explanation of benefit.
   */
  identifier?: Identifier[];
  /**
   * All insurance coverages for the patient which may be applicable for reimbursement, of the products and services listed in the claim, are typically provided in the claim to allow insurers to confirm the ordering of the insurance coverages relative to local 'coordination of benefit' rules. One coverage (and only one) with 'focal=true' is to be used in the adjudication of this claim. Coverages appearing before the focal Coverage in the list, and where 'Coverage.subrogation=false', should provide a reference to the ClaimResponse containing the adjudication results of the prior claim.
   */
  insurance: ExplanationOfBenefitInsurance[];
  /**
   * The party responsible for authorization, adjudication and reimbursement.
   */
  insurer: Reference;
  /**
   * A claim line. Either a simple (a product or service) or a 'group' of details which can also be a simple items or groups of sub-details.
   */
  item?: ExplanationOfBenefitItem[];
  /**
   * For example, a physician may prescribe a medication which the pharmacy determines is contraindicated, or for which the patient has an intolerance, and therefor issues a new prescription for an alternate medication which has the same therapeutic intent. The prescription from the pharmacy becomes the 'prescription' and that from the physician becomes the 'original prescription'.
   */
  originalPrescription?: Reference;
  /**
   * The resource may be used to indicate that: the request has been held (queued) for processing; that it has been processed and errors found (error); that no errors were found and that some of the adjudication has been undertaken (partial) or that all of the adjudication has been undertaken (complete).
   */
  outcome: string;
  _outcome?: Element;
  /**
   * The party to whom the professional services and/or products have been supplied or are being considered and for whom actual for forecast reimbursement is sought.
   */
  patient: Reference;
  /**
   * Often providers agree to receive the benefits payable to reduce the near-term costs to the patient. The insurer may decline to pay the provider and may choose to pay the subscriber instead.
   */
  payee?: ExplanationOfBenefitPayee;
  /**
   * Payment details for the adjudication of the claim.
   */
  payment?: ExplanationOfBenefitPayment;
  /**
   * This value is only present on preauthorization adjudications.
   */
  preAuthRef?: string[];
  _preAuthRef?: Element;
  /**
   * This value is only present on preauthorization adjudications.
   */
  preAuthRefPeriod?: Period[];
  /**
   * This indicates the relative order of a series of EOBs related to different coverages for the same suite of services.
   */
  precedence?: number;
  /**
   * Prescription to support the dispensing of pharmacy, device or vision products.
   */
  prescription?: Reference;
  /**
   * If a claim processor is unable to complete the processing as per the priority then they should generate and error and not process the request.
   */
  priority?: CodeableConcept;
  /**
   * Procedures performed on the patient relevant to the billing items with the claim.
   */
  procedure?: ExplanationOfBenefitProcedure[];
  /**
   * A note that describes or explains adjudication results in a human readable form.
   */
  processNote?: ExplanationOfBenefitProcessNote[];
  /**
   * Typically this field would be 1..1 where this party is responsible for the claim but not necessarily professionally responsible for the provision of the individual products and services listed below.
   */
  provider: Reference;
  /**
   * The referral resource which lists the date, practitioner, reason and other supporting information.
   */
  referral?: Reference;
  /**
   * For example,  for the original treatment and follow-up exams.
   */
  related?: ExplanationOfBenefitRelated[];
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * This may contain the local bill type codes such as the US UB-04 bill type code.
   */
  subType?: CodeableConcept;
  /**
   * Often there are multiple jurisdiction specific valuesets which are required.
   */
  supportingInfo?: ExplanationOfBenefitSupportingInfo[];
  /**
   * Totals for amounts submitted, co-pays, benefits payable etc.
   */
  total?: ExplanationOfBenefitTotal[];
  /**
   * The majority of jurisdictions use: oral, pharmacy, vision, professional and institutional, or variants on those terms, as the general styles of claims. The valueset is extensible to accommodate other jurisdictional requirements.
   */
  type: CodeableConcept;
  /**
   * A code to indicate whether the nature of the request is: to request adjudication of products and services previously rendered; or requesting authorization and adjudication for provision in the future; or requesting the non-binding adjudication of the listed products and services which could be provided in the future.
   */
  use: string;
  _use?: Element;
}
/**
 * Code Values for the ExplanationOfBenefit.outcome field
 */
export enum ExplanationOfBenefitOutcomeCodes {
  QUEUED = 'queued',
  COMPLETE = 'complete',
  ERROR = 'error',
  PARTIAL = 'partial',
}
/**
 * Code Values for the ExplanationOfBenefit.status field
 */
export enum ExplanationOfBenefitStatusCodes {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  DRAFT = 'draft',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Code Values for the ExplanationOfBenefit.use field
 */
export enum ExplanationOfBenefitUseCodes {
  CLAIM = 'claim',
  PREAUTHORIZATION = 'preauthorization',
  PREDETERMINATION = 'predetermination',
}
/**
 * The significant Conditions (or condition) that the family member had. This is a repeating section to allow a system to represent more than one condition per resource, though there is nothing stopping multiple resources - one per condition.
 */
export interface FamilyMemberHistoryCondition extends BackboneElement {
  /**
   * The actual condition specified. Could be a coded condition (like MI or Diabetes) or a less specific string like 'cancer' depending on how much is known about the condition and the capabilities of the creating system.
   */
  code: CodeableConcept;
  /**
   * This condition contributed to the cause of death of the related person. If contributedToDeath is not populated, then it is unknown.
   */
  contributedToDeath?: boolean;
  _contributedToDeath?: Element;
  /**
   * An area where general notes can be placed about this specific condition.
   */
  note?: Annotation[];
  /**
   * Either the age of onset, range of approximate age or descriptive string can be recorded.  For conditions with multiple occurrences, this describes the first known occurrence.
   */
  onsetAge?: Age;
  /**
   * Either the age of onset, range of approximate age or descriptive string can be recorded.  For conditions with multiple occurrences, this describes the first known occurrence.
   */
  onsetRange?: Range;
  /**
   * Either the age of onset, range of approximate age or descriptive string can be recorded.  For conditions with multiple occurrences, this describes the first known occurrence.
   */
  onsetPeriod?: Period;
  /**
   * Either the age of onset, range of approximate age or descriptive string can be recorded.  For conditions with multiple occurrences, this describes the first known occurrence.
   */
  onsetString?: string;
  _onsetString?: Element;
  /**
   * Indicates what happened following the condition.  If the condition resulted in death, deceased date is captured on the relation.
   */
  outcome?: CodeableConcept;
}
/**
 * Significant health conditions for a person related to the patient relevant in the context of care for the patient.
 */
export interface FamilyMemberHistory extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'FamilyMemberHistory';
  /**
   * use estimatedAge to indicate whether the age is actual or not.
   */
  ageAge?: Age;
  /**
   * use estimatedAge to indicate whether the age is actual or not.
   */
  ageRange?: Range;
  /**
   * use estimatedAge to indicate whether the age is actual or not.
   */
  ageString?: string;
  _ageString?: Element;
  /**
   * The actual or approximate date of birth of the relative.
   */
  bornPeriod?: Period;
  /**
   * The actual or approximate date of birth of the relative.
   */
  bornDate?: string;
  _bornDate?: Element;
  /**
   * The actual or approximate date of birth of the relative.
   */
  bornString?: string;
  _bornString?: Element;
  /**
   * The significant Conditions (or condition) that the family member had. This is a repeating section to allow a system to represent more than one condition per resource, though there is nothing stopping multiple resources - one per condition.
   */
  condition?: FamilyMemberHistoryCondition[];
  /**
   * Describes why the family member's history is not available.
   */
  dataAbsentReason?: CodeableConcept;
  /**
   * This should be captured even if the same as the date on the List aggregating the full family history.
   */
  date?: string;
  _date?: Element;
  /**
   * Deceased flag or the actual or approximate age of the relative at the time of death for the family member history record.
   */
  deceasedBoolean?: boolean;
  _deceasedBoolean?: Element;
  /**
   * Deceased flag or the actual or approximate age of the relative at the time of death for the family member history record.
   */
  deceasedAge?: Age;
  /**
   * Deceased flag or the actual or approximate age of the relative at the time of death for the family member history record.
   */
  deceasedRange?: Range;
  /**
   * Deceased flag or the actual or approximate age of the relative at the time of death for the family member history record.
   */
  deceasedDate?: string;
  _deceasedDate?: Element;
  /**
   * Deceased flag or the actual or approximate age of the relative at the time of death for the family member history record.
   */
  deceasedString?: string;
  _deceasedString?: Element;
  /**
   * This element is labeled as a modifier because the fact that age is estimated can/should change the results of any algorithm that calculates based on the specified age.
   */
  estimatedAge?: boolean;
  _estimatedAge?: Element;
  /**
   * This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  identifier?: Identifier[];
  /**
   * The URL pointing to a FHIR-defined protocol, guideline, orderset or other definition that is adhered to in whole or in part by this FamilyMemberHistory.
   */
  instantiatesCanonical?: string[];
  _instantiatesCanonical?: Element;
  /**
   * This might be an HTML page, PDF, etc. or could just be a non-resolvable URI identifier.
   */
  instantiatesUri?: string[];
  _instantiatesUri?: Element;
  /**
   * This will either be a name or a description; e.g. "Aunt Susan", "my cousin with the red hair".
   */
  name?: string;
  _name?: Element;
  /**
   * This property allows a non condition-specific note to the made about the related person. Ideally, the note would be in the condition property, but this is not always possible.
   */
  note?: Annotation[];
  /**
   * The person who this history concerns.
   */
  patient: Reference;
  /**
   * Textual reasons can be captured using reasonCode.text.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Indicates a Condition, Observation, AllergyIntolerance, or QuestionnaireResponse that justifies this family member history event.
   */
  reasonReference?: Reference[];
  /**
   * The type of relationship this person has to the patient (father, mother, brother etc.).
   */
  relationship: CodeableConcept;
  /**
   * This element should ideally reflect whether the individual is genetically male or female.  However, as reported information based on the knowledge of the patient or reporting friend/relative, there may be situations where the reported sex might not be totally accurate.  E.g. 'Aunt Sue' might be XY rather than XX.  Questions soliciting this information should be phrased to encourage capture of genetic sex where known.  However, systems performing analysis should also allow for the possibility of imprecision with this element.
   */
  sex?: CodeableConcept;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
}
/**
 * Code Values for the FamilyMemberHistory.status field
 */
export enum FamilyMemberHistoryStatusCodes {
  PARTIAL = 'partial',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  HEALTH_UNKNOWN = 'health-unknown',
}
/**
 * Prospective warnings of potential issues when providing care to the patient.
 */
export interface Flag extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Flag';
  /**
   * The person, organization or device that created the flag.
   */
  author?: Reference;
  /**
   * The value set will often need to be adjusted based on local business rules and usage context.
   */
  category?: CodeableConcept[];
  /**
   * If non-coded, use CodeableConcept.text.  This element should always be included in the narrative.
   */
  code: CodeableConcept;
  /**
   * If both Flag.encounter and Flag.period are valued, then Flag.period.start shall not be before Encounter.period.start and Flag.period.end shall not be after Encounter.period.end.
   */
  encounter?: Reference;
  /**
   * This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  identifier?: Identifier[];
  /**
   * The period of time from the activation of the flag to inactivation of the flag. If the flag is active, the end of the period should be unspecified.
   */
  period?: Period;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * The patient, location, group, organization, or practitioner etc. this is about record this flag is associated with.
   */
  subject: Reference;
}
/**
 * Code Values for the Flag.status field
 */
export enum FlagStatusCodes {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * When multiple targets are present for a single goal instance, all targets must be met for the overall goal to be met.
 */
export interface GoalTarget extends BackboneElement {
  /**
   * A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Goal.target.measure defines a coded value.
   */
  detailQuantity?: Quantity;
  /**
   * A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Goal.target.measure defines a coded value.
   */
  detailRange?: Range;
  /**
   * A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Goal.target.measure defines a coded value.
   */
  detailCodeableConcept?: CodeableConcept;
  /**
   * A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Goal.target.measure defines a coded value.
   */
  detailString?: string;
  _detailString?: Element;
  /**
   * A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Goal.target.measure defines a coded value.
   */
  detailBoolean?: boolean;
  _detailBoolean?: Element;
  /**
   * A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Goal.target.measure defines a coded value.
   */
  detailInteger?: number;
  /**
   * A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Goal.target.measure defines a coded value.
   */
  detailRatio?: Ratio;
  /**
   * Indicates either the date or the duration after start by which the goal should be met.
   */
  dueDate?: string;
  _dueDate?: Element;
  /**
   * Indicates either the date or the duration after start by which the goal should be met.
   */
  dueDuration?: Duration;
  /**
   * The parameter whose value is being tracked, e.g. body weight, blood pressure, or hemoglobin A1c level.
   */
  measure?: CodeableConcept;
}
/**
 * Describes the intended objective(s) for a patient, group or organization care, for example, weight loss, restoring an activity of daily living, obtaining herd immunity via immunization, meeting a process improvement objective, etc.
 */
export interface Goal extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Goal';
  /**
   * Describes the progression, or lack thereof, towards the goal against the target.
   */
  achievementStatus?: CodeableConcept;
  /**
   * The identified conditions and other health record elements that are intended to be addressed by the goal.
   */
  addresses?: Reference[];
  /**
   * Indicates a category the goal falls within.
   */
  category?: CodeableConcept[];
  /**
   * If no code is available, use CodeableConcept.text.
   */
  description: CodeableConcept;
  /**
   * This is the individual responsible for establishing the goal, not necessarily who recorded it.  (For that, use the Provenance resource.).
   */
  expressedBy?: Reference;
  /**
   * This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  identifier?: Identifier[];
  /**
   * This element is labeled as a modifier because the lifecycleStatus contains codes that mark the resource as not currently valid.
   */
  lifecycleStatus: string;
  _lifecycleStatus?: Element;
  /**
   * May be used for progress notes, concerns or other related information that doesn't actually describe the goal itself.
   */
  note?: Annotation[];
  /**
   * Note that this should not duplicate the goal status.
   */
  outcomeCode?: CodeableConcept[];
  /**
   * The goal outcome is independent of the outcome of the related activities.  For example, if the Goal is to achieve a target body weight of 150 lb and a care plan activity is defined to diet, then the care plan’s activity outcome could be calories consumed whereas goal outcome is an observation for the actual body weight measured.
   */
  outcomeReference?: Reference[];
  /**
   * Extensions are available to track priorities as established by each participant (i.e. Priority from the patient's perspective, different practitioners' perspectives, family member's perspectives)
   * The ordinal extension on Coding can be used to convey a numerically comparable ranking to priority.  (Keep in mind that different coding systems may use a "low value=important".
   */
  priority?: CodeableConcept;
  /**
   * The date or event after which the goal should begin being pursued.
   */
  startDate?: string;
  _startDate?: Element;
  /**
   * The date or event after which the goal should begin being pursued.
   */
  startCodeableConcept?: CodeableConcept;
  /**
   * To see the date for past statuses, query history.
   */
  statusDate?: string;
  _statusDate?: Element;
  /**
   * This will typically be captured for statuses such as rejected, on-hold or cancelled, but could be present for others.
   */
  statusReason?: string;
  _statusReason?: Element;
  /**
   * Identifies the patient, group or organization for whom the goal is being established.
   */
  subject: Reference;
  /**
   * When multiple targets are present for a single goal instance, all targets must be met for the overall goal to be met.
   */
  target?: GoalTarget[];
}
/**
 * Code Values for the Goal.lifecycleStatus field
 */
export enum GoalLifecycleStatusCodes {
  PROPOSED = 'proposed',
  PLANNED = 'planned',
  ACCEPTED = 'accepted',
  ACTIVE = 'active',
  ON_HOLD = 'on-hold',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  ENTERED_IN_ERROR = 'entered-in-error',
  REJECTED = 'rejected',
}
/**
 * Compartment Consistency Rules.
 */
export interface GraphDefinitionLinkTargetCompartment extends BackboneElement {
  /**
   * Identifies the compartment.
   */
  code: string;
  _code?: Element;
  /**
   * Documentation for FHIRPath expression.
   */
  description?: string;
  _description?: Element;
  /**
   * Custom rule, as a FHIRPath expression.
   */
  expression?: string;
  _expression?: Element;
  /**
   * identical | matching | different | no-rule | custom.
   */
  rule: string;
  _rule?: Element;
  /**
   * All conditional rules are evaluated; if they are true, then the rules are evaluated.
   */
  use: string;
  _use?: Element;
}
/**
 * Code Values for the GraphDefinition.link.target.compartment.code field
 */
export enum GraphDefinitionLinkTargetCompartmentCodeCodes {
  PATIENT = 'Patient',
  ENCOUNTER = 'Encounter',
  RELATEDPERSON = 'RelatedPerson',
  PRACTITIONER = 'Practitioner',
  DEVICE = 'Device',
}
/**
 * Code Values for the GraphDefinition.link.target.compartment.rule field
 */
export enum GraphDefinitionLinkTargetCompartmentRuleCodes {
  IDENTICAL = 'identical',
  MATCHING = 'matching',
  DIFFERENT = 'different',
  CUSTOM = 'custom',
}
/**
 * Code Values for the GraphDefinition.link.target.compartment.use field
 */
export enum GraphDefinitionLinkTargetCompartmentUseCodes {
  CONDITION = 'condition',
  REQUIREMENT = 'requirement',
}
/**
 * Potential target for the link.
 */
export interface GraphDefinitionLinkTarget extends BackboneElement {
  /**
   * Compartment Consistency Rules.
   */
  compartment?: GraphDefinitionLinkTargetCompartment[];
  /**
   * Additional links from target resource.
   */
  link?: GraphDefinitionLink[];
  /**
   * At least one of the parameters must have the value {ref} which identifies the focus resource.
   */
  params?: string;
  _params?: Element;
  /**
   * Profile for the target resource.
   */
  profile?: string;
  _profile?: Element;
  /**
   * Type of resource this link refers to.
   */
  type: string;
  _type?: Element;
}
/**
 * Links this graph makes rules about.
 */
export interface GraphDefinitionLink extends BackboneElement {
  /**
   * Information about why this link is of interest in this graph definition.
   */
  description?: string;
  _description?: Element;
  /**
   * Maximum occurrences for this link.
   */
  max?: string;
  _max?: Element;
  /**
   * Minimum occurrences for this link.
   */
  min?: number;
  /**
   * The path expression cannot contain a resolve() function. If there is no path, the link is a reverse lookup, using target.params. If the path is "*" then this means all references in the resource.
   */
  path?: string;
  _path?: Element;
  /**
   * Which slice (if profiled).
   */
  sliceName?: string;
  _sliceName?: Element;
  /**
   * Potential target for the link.
   */
  target?: GraphDefinitionLinkTarget[];
}
/**
 * A formal computable definition of a graph of resources - that is, a coherent set of resources that form a graph by following references. The Graph Definition resource defines a set and makes rules about the set.
 */
export interface GraphDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'GraphDefinition';
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the graph definition. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the graph definition was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the graph definition as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the graph definition is presumed to be the predominant language in the place the graph definition was created).
   */
  description?: string;
  _description?: Element;
  /**
   * Allows filtering of graph definitions that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * It may be possible for the graph definition to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * Links this graph makes rules about.
   */
  link?: GraphDefinitionLink[];
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name: string;
  _name?: Element;
  /**
   * The code does not include the '$' prefix that is always included in the URL when the operation is invoked.
   */
  profile?: string;
  _profile?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the graph definition is the organization or individual primarily responsible for the maintenance and upkeep of the graph definition. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the graph definition. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the graph definition. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this graph definition.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * The type of FHIR resource at which instances of this graph start.
   */
  start: string;
  _start?: Element;
  /**
   * Allows filtering of graph definitions that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different graph definition instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the graph definition with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the GraphDefinition.status field
 */
export enum GraphDefinitionStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * All the identified characteristics must be true for an entity to a member of the group.
 */
export interface GroupCharacteristic extends BackboneElement {
  /**
   * A code that identifies the kind of trait being asserted.
   */
  code: CodeableConcept;
  /**
   * This is labeled as "Is Modifier" because applications cannot wrongly include excluded members as included or vice versa.
   */
  exclude: boolean;
  _exclude?: Element;
  /**
   * The period over which the characteristic is tested; e.g. the patient had an operation during the month of June.
   */
  period?: Period;
  /**
   * For Range, it means members of the group have a value that falls somewhere within the specified range.
   */
  valueCodeableConcept: CodeableConcept;
  /**
   * For Range, it means members of the group have a value that falls somewhere within the specified range.
   */
  valueBoolean: boolean;
  _valueBoolean?: Element;
  /**
   * For Range, it means members of the group have a value that falls somewhere within the specified range.
   */
  valueQuantity: Quantity;
  /**
   * For Range, it means members of the group have a value that falls somewhere within the specified range.
   */
  valueRange: Range;
  /**
   * For Range, it means members of the group have a value that falls somewhere within the specified range.
   */
  valueReference: Reference;
}
/**
 * Identifies the resource instances that are members of the group.
 */
export interface GroupMember extends BackboneElement {
  /**
   * A reference to the entity that is a member of the group. Must be consistent with Group.type. If the entity is another group, then the type must be the same.
   */
  entity: Reference;
  /**
   * A flag to indicate that the member is no longer in the group, but previously may have been a member.
   */
  inactive?: boolean;
  _inactive?: Element;
  /**
   * The period that the member was in the group, if known.
   */
  period?: Period;
}
/**
 * Represents a defined collection of entities that may be discussed or acted upon collectively but which are not expected to act collectively, and are not formally or legally recognized; i.e. a collection of entities that isn't an Organization.
 */
export interface Group extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Group';
  /**
   * Indicates whether the record for the group is available for use or is merely being retained for historical purposes.
   */
  active?: boolean;
  _active?: Element;
  /**
   * If true, indicates that the resource refers to a specific group of real individuals.  If false, the group defines a set of intended individuals.
   */
  actual: boolean;
  _actual?: Element;
  /**
   * All the identified characteristics must be true for an entity to a member of the group.
   */
  characteristic?: GroupCharacteristic[];
  /**
   * This would generally be omitted for Person resources.
   */
  code?: CodeableConcept;
  /**
   * A unique business identifier for this group.
   */
  identifier?: Identifier[];
  /**
   * This does not strictly align with ownership of a herd or flock, but may suffice to represent that relationship in simple cases. More complex cases will require an extension.
   */
  managingEntity?: Reference;
  /**
   * Identifies the resource instances that are members of the group.
   */
  member?: GroupMember[];
  /**
   * A label assigned to the group for human identification and communication.
   */
  name?: string;
  _name?: Element;
  /**
   * Note that the quantity may be less than the number of members if some of the members are not active.
   */
  quantity?: number;
  /**
   * Group members SHALL be of the appropriate resource type (Patient for person or animal; or Practitioner, Device, Medication or Substance for the other types.).
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the Group.type field
 */
export enum GroupTypeCodes {
  PERSON = 'person',
  ANIMAL = 'animal',
  PRACTITIONER = 'practitioner',
  DEVICE = 'device',
  MEDICATION = 'medication',
  SUBSTANCE = 'substance',
}
/**
 * A guidance response is the formal response to a guidance request, including any output parameters returned by the evaluation, as well as the description of any proposed actions to be taken.
 */
export interface GuidanceResponse extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'GuidanceResponse';
  /**
   * If the evaluation could not be completed due to lack of information, or additional information would potentially result in a more accurate response, this element will a description of the data required in order to proceed with the evaluation. A subsequent request to the service should include this data.
   */
  dataRequirement?: DataRequirement[];
  /**
   * This will typically be the encounter the event occurred within, but some activities may be initiated prior to or after the official copmletion of an encounter but still be tied to the context of the encounter.
   */
  encounter?: Reference;
  /**
   * Messages resulting from the evaluation of the artifact or artifacts. As part of evaluating the request, the engine may produce informational or warning messages. These messages will be provided by this element.
   */
  evaluationMessage?: Reference[];
  /**
   * Allows a service to provide  unique, business identifiers for the response.
   */
  identifier?: Identifier[];
  /**
   * An identifier, CodeableConcept or canonical reference to the guidance that was requested.
   */
  moduleUri: string;
  _moduleUri?: Element;
  /**
   * An identifier, CodeableConcept or canonical reference to the guidance that was requested.
   */
  moduleCanonical: string;
  _moduleCanonical?: Element;
  /**
   * An identifier, CodeableConcept or canonical reference to the guidance that was requested.
   */
  moduleCodeableConcept: CodeableConcept;
  /**
   * Provides a mechanism to communicate additional information about the response.
   */
  note?: Annotation[];
  /**
   * Indicates when the guidance response was processed.
   */
  occurrenceDateTime?: string;
  _occurrenceDateTime?: Element;
  /**
   * The output parameters of the evaluation, if any. Many modules will result in the return of specific resources such as procedure or communication requests that are returned as part of the operation result. However, modules may define specific outputs that would be returned as the result of the evaluation, and these would be returned in this element.
   */
  outputParameters?: Reference;
  /**
   * Provides a reference to the device that performed the guidance.
   */
  performer?: Reference;
  /**
   * Describes the reason for the guidance response in coded or textual form.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Indicates the reason the request was initiated. This is typically provided as a parameter to the evaluation and echoed by the service, although for some use cases, such as subscription- or event-based scenarios, it may provide an indication of the cause for the response.
   */
  reasonReference?: Reference[];
  /**
   * The identifier of the request associated with this response. If an identifier was given as part of the request, it will be reproduced here to enable the requester to more easily identify the response in a multi-request scenario.
   */
  requestIdentifier?: Identifier;
  /**
   * The actions, if any, produced by the evaluation of the artifact.
   */
  result?: Reference;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * The patient for which the request was processed.
   */
  subject?: Reference;
}
/**
 * Code Values for the GuidanceResponse.status field
 */
export enum GuidanceResponseStatusCodes {
  SUCCESS = 'success',
  DATA_REQUESTED = 'data-requested',
  DATA_REQUIRED = 'data-required',
  IN_PROGRESS = 'in-progress',
  FAILURE = 'failure',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Does this service have specific eligibility requirements that need to be met in order to use the service?
 */
export interface HealthcareServiceEligibility extends BackboneElement {
  /**
   * Coded value for the eligibility.
   */
  code?: CodeableConcept;
  /**
   * The description of service eligibility should, in general, not exceed one or two paragraphs. It should be sufficient for a prospective consumer to determine if they are likely to be eligible or not. Where eligibility requirements and conditions are complex, it may simply be noted that an eligibility assessment is required. Where eligibility is determined by an outside source, such as an Act of Parliament, this should be noted, preferably with a reference to a commonly available copy of the source document such as a web page.
   */
  comment?: string;
  _comment?: Element;
}
/**
 * More detailed availability information may be provided in associated Schedule/Slot resources.
 */
export interface HealthcareServiceAvailableTime extends BackboneElement {
  /**
   * Is this always available? (hence times are irrelevant) e.g. 24 hour service.
   */
  allDay?: boolean;
  _allDay?: Element;
  /**
   * The time zone is expected to be for where this HealthcareService is provided at.
   */
  availableEndTime?: string;
  _availableEndTime?: Element;
  /**
   * The time zone is expected to be for where this HealthcareService is provided at.
   */
  availableStartTime?: string;
  _availableStartTime?: Element;
  /**
   * Indicates which days of the week are available between the start and end Times.
   */
  daysOfWeek?: string[];
  _daysOfWeek?: Element;
}
/**
 * Code Values for the HealthcareService.availableTime.daysOfWeek field
 */
export enum HealthcareServiceAvailableTimeDaysOfWeekCodes {
  MON = 'mon',
  TUE = 'tue',
  WED = 'wed',
  THU = 'thu',
  FRI = 'fri',
  SAT = 'sat',
  SUN = 'sun',
}
/**
 * The HealthcareService is not available during this period of time due to the provided reason.
 */
export interface HealthcareServiceNotAvailable extends BackboneElement {
  /**
   * The reason that can be presented to the user as to why this time is not available.
   */
  description: string;
  _description?: Element;
  /**
   * Service is not available (seasonally or for a public holiday) from this date.
   */
  during?: Period;
}
/**
 * The details of a healthcare service available at a location.
 */
export interface HealthcareService extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'HealthcareService';
  /**
   * This element is labeled as a modifier because it may be used to mark that the resource was created in error.
   */
  active?: boolean;
  _active?: Element;
  /**
   * Indicates whether or not a prospective consumer will require an appointment for a particular service at a site to be provided by the Organization. Indicates if an appointment is required for access to this service.
   */
  appointmentRequired?: boolean;
  _appointmentRequired?: Element;
  /**
   * A description of site availability exceptions, e.g. public holiday availability. Succinctly describing all possible exceptions to normal site availability as details in the available Times and not available Times.
   */
  availabilityExceptions?: string;
  _availabilityExceptions?: Element;
  /**
   * More detailed availability information may be provided in associated Schedule/Slot resources.
   */
  availableTime?: HealthcareServiceAvailableTime[];
  /**
   * Selecting a Service Category then determines the list of relevant service types that can be selected in the primary service type.
   */
  category?: CodeableConcept[];
  /**
   * These could be such things as is wheelchair accessible.
   */
  characteristic?: CodeableConcept[];
  /**
   * Would expect that a user would not see this information on a search results, and it would only be available when viewing the complete details of the service.
   */
  comment?: string;
  _comment?: Element;
  /**
   * When using this property it indicates that the service is available with this language, it is not derived from the practitioners, and not all are required to use this language, just that this language is available while scheduling.
   */
  communication?: CodeableConcept[];
  /**
   * The locations referenced by the coverage area can include both specific locations, including areas, and also conceptual domains too (mode = kind), such as a physical area (tri-state area) and some other attribute (covered by Example Care Organization). These types of Locations are often not managed by any specific organization. This could also include generic locations such as "in-home".
   */
  coverageArea?: Reference[];
  /**
   * Does this service have specific eligibility requirements that need to be met in order to use the service?
   */
  eligibility?: HealthcareServiceEligibility[];
  /**
   * Technical endpoints providing access to services operated for the specific healthcare services defined at this resource.
   */
  endpoint?: Reference[];
  /**
   * Extra details about the service that can't be placed in the other fields.
   */
  extraDetails?: string;
  _extraDetails?: Element;
  /**
   * External identifiers for this item.
   */
  identifier?: Identifier[];
  /**
   * The location(s) where this healthcare service may be provided.
   */
  location?: Reference[];
  /**
   * Further description of the service as it would be presented to a consumer while searching.
   */
  name?: string;
  _name?: Element;
  /**
   * The HealthcareService is not available during this period of time due to the provided reason.
   */
  notAvailable?: HealthcareServiceNotAvailable[];
  /**
   * If there is a photo/symbol associated with this HealthcareService, it may be included here to facilitate quick identification of the service in a list.
   */
  photo?: Attachment;
  /**
   * Programs are often defined externally to an Organization, commonly by governments; e.g. Home and Community Care Programs, Homeless Program, ….
   */
  program?: CodeableConcept[];
  /**
   * This property is recommended to be the same as the Location's managingOrganization, and if not provided should be interpreted as such. If the Location does not have a managing Organization, then this property should be populated.
   */
  providedBy?: Reference;
  /**
   * Ways that the service accepts referrals, if this is not provided then it is implied that no referral is required.
   */
  referralMethod?: CodeableConcept[];
  /**
   * The provision means being commissioned by, contractually obliged or financially sourced. Types of costings that may apply to this healthcare service, such if the service may be available for free, some discounts available, or fees apply.
   */
  serviceProvisionCode?: CodeableConcept[];
  /**
   * Collection of specialties handled by the service site. This is more of a medical term.
   */
  specialty?: CodeableConcept[];
  /**
   * If this is empty, then refer to the location's contacts.
   */
  telecom?: ContactPoint[];
  /**
   * The specific type of service that may be delivered or performed.
   */
  type?: CodeableConcept[];
}
/**
 * If the person who performed the series is not known, their Organization may be recorded. A patient, or related person, may be the performer, e.g. for patient-captured images.
 */
export interface ImagingStudySeriesPerformer extends BackboneElement {
  /**
   * Indicates who or what performed the series.
   */
  actor: Reference;
  /**
   * Distinguishes the type of involvement of the performer in the series.
   */
  function?: CodeableConcept;
}
/**
 * A single SOP instance within the series, e.g. an image, or presentation state.
 */
export interface ImagingStudySeriesInstance extends BackboneElement {
  /**
   * The number of instance in the series.
   */
  number?: number;
  /**
   * DICOM instance  type.
   */
  sopClass: Coding;
  /**
   * Particularly for post-acquisition analytic objects, such as SR, presentation states, value mapping, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * See  [DICOM PS3.3 C.12.1](http://dicom.nema.org/medical/dicom/current/output/chtml/part03/sect_C.12.html#sect_C.12.1).
   */
  uid: string;
  _uid?: Element;
}
/**
 * Each study has one or more series of images or other content.
 */
export interface ImagingStudySeries extends BackboneElement {
  /**
   * The anatomic structures examined. See DICOM Part 16 Annex L (http://dicom.nema.org/medical/dicom/current/output/chtml/part16/chapter_L.html) for DICOM to SNOMED-CT mappings. The bodySite may indicate the laterality of body part imaged; if so, it shall be consistent with any content of ImagingStudy.series.laterality.
   */
  bodySite?: Coding;
  /**
   * A description of the series.
   */
  description?: string;
  _description?: Element;
  /**
   * Typical endpoint types include DICOM WADO-RS, which is used to retrieve DICOM instances in native or rendered (e.g., JPG, PNG) formats using a RESTful API; DICOM WADO-URI, which can similarly retrieve native or rendered instances, except using an HTTP query-based approach; and DICOM QIDO-RS, which allows RESTful query for DICOM information without retrieving the actual instances.
   */
  endpoint?: Reference[];
  /**
   * A single SOP instance within the series, e.g. an image, or presentation state.
   */
  instance?: ImagingStudySeriesInstance[];
  /**
   * The laterality of the (possibly paired) anatomic structures examined. E.g., the left knee, both lungs, or unpaired abdomen. If present, shall be consistent with any laterality information indicated in ImagingStudy.series.bodySite.
   */
  laterality?: Coding;
  /**
   * The modality of this series sequence.
   */
  modality: Coding;
  /**
   * The numeric identifier of this series in the study.
   */
  number?: number;
  /**
   * Number of SOP Instances in the Study. The value given may be larger than the number of instance elements this resource contains due to resource availability, security, or other factors. This element should be present if any instance elements are present.
   */
  numberOfInstances?: number;
  /**
   * If the person who performed the series is not known, their Organization may be recorded. A patient, or related person, may be the performer, e.g. for patient-captured images.
   */
  performer?: ImagingStudySeriesPerformer[];
  /**
   * The specimen imaged, e.g., for whole slide imaging of a biopsy.
   */
  specimen?: Reference[];
  /**
   * The date and time the series was started.
   */
  started?: string;
  _started?: Element;
  /**
   * See [DICOM PS3.3 C.7.3](http://dicom.nema.org/medical/dicom/current/output/chtml/part03/sect_C.7.3.html).
   */
  uid: string;
  _uid?: Element;
}
/**
 * Representation of the content produced in a DICOM imaging study. A study comprises a set of series, each of which includes a set of Service-Object Pair Instances (SOP Instances - images or other data) acquired or produced in a common context.  A series is of only one modality (e.g. X-ray, CT, MR, ultrasound), but a study may have multiple series of different modalities.
 */
export interface ImagingStudy extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ImagingStudy';
  /**
   * A list of the diagnostic requests that resulted in this imaging study being performed.
   */
  basedOn?: Reference[];
  /**
   * The Imaging Manager description of the study. Institution-generated description or classification of the Study (component) performed.
   */
  description?: string;
  _description?: Element;
  /**
   * This will typically be the encounter the event occurred within, but some events may be initiated prior to or after the official completion of an encounter but still be tied to the context of the encounter (e.g. pre-admission test).
   */
  encounter?: Reference;
  /**
   * Typical endpoint types include DICOM WADO-RS, which is used to retrieve DICOM instances in native or rendered (e.g., JPG, PNG), formats using a RESTful API; DICOM WADO-URI, which can similarly retrieve native or rendered instances, except using an HTTP query-based approach; DICOM QIDO-RS, which allows RESTful query for DICOM information without retrieving the actual instances; or IHE Invoke Image Display (IID), which provides standard invocation of an imaging web viewer.
   */
  endpoint?: Reference[];
  /**
   * See discussion under [Imaging Study Implementation Notes](imagingstudy.html#notes) for encoding of DICOM Study Instance UID. Accession Number should use ACSN Identifier type.
   */
  identifier?: Identifier[];
  /**
   * Who read the study and interpreted the images or other content.
   */
  interpreter?: Reference[];
  /**
   * The principal physical location where the ImagingStudy was performed.
   */
  location?: Reference;
  /**
   * A list of all the series.modality values that are actual acquisition modalities, i.e. those in the DICOM Context Group 29 (value set OID 1.2.840.10008.6.1.19).
   */
  modality?: Coding[];
  /**
   * Per the recommended DICOM mapping, this element is derived from the Study Description attribute (0008,1030). Observations or findings about the imaging study should be recorded in another resource, e.g. Observation, and not in this element.
   */
  note?: Annotation[];
  /**
   * Number of SOP Instances in Study. This value given may be larger than the number of instance elements this resource contains due to resource availability, security, or other factors. This element should be present if any instance elements are present.
   */
  numberOfInstances?: number;
  /**
   * Number of Series in the Study. This value given may be larger than the number of series elements this Resource contains due to resource availability, security, or other factors. This element should be present if any series elements are present.
   */
  numberOfSeries?: number;
  /**
   * The code for the performed procedure type.
   */
  procedureCode?: CodeableConcept[];
  /**
   * The procedure which this ImagingStudy was part of.
   */
  procedureReference?: Reference;
  /**
   * Description of clinical condition indicating why the ImagingStudy was requested.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Indicates another resource whose existence justifies this Study.
   */
  reasonReference?: Reference[];
  /**
   * The requesting/referring physician.
   */
  referrer?: Reference;
  /**
   * Each study has one or more series of images or other content.
   */
  series?: ImagingStudySeries[];
  /**
   * Date and time the study started.
   */
  started?: string;
  _started?: Element;
  /**
   * Unknown does not represent "other" - one of the defined statuses must apply.  Unknown is used when the authoring system is not sure what the current status is.
   */
  status: string;
  _status?: Element;
  /**
   * QA phantoms can be recorded with a Device; multiple subjects (such as mice) can be recorded with a Group.
   */
  subject: Reference;
}
/**
 * Code Values for the ImagingStudy.status field
 */
export enum ImagingStudyStatusCodes {
  REGISTERED = 'registered',
  AVAILABLE = 'available',
  CANCELLED = 'cancelled',
  ENTERED_IN_ERROR = 'entered-in-error',
  UNKNOWN = 'unknown',
}
/**
 * Indicates who performed the immunization event.
 */
export interface ImmunizationPerformer extends BackboneElement {
  /**
   * When the individual practitioner who performed the action is known, it is best to send.
   */
  actor: Reference;
  /**
   * Describes the type of performance (e.g. ordering provider, administering provider, etc.).
   */
  function?: CodeableConcept;
}
/**
 * Educational material presented to the patient (or guardian) at the time of vaccine administration.
 */
export interface ImmunizationEducation extends BackboneElement {
  /**
   * Identifier of the material presented to the patient.
   */
  documentType?: string;
  _documentType?: Element;
  /**
   * Date the educational material was given to the patient.
   */
  presentationDate?: string;
  _presentationDate?: Element;
  /**
   * Date the educational material was published.
   */
  publicationDate?: string;
  _publicationDate?: Element;
  /**
   * Reference pointer to the educational material given to the patient if the information was on line.
   */
  reference?: string;
  _reference?: Element;
}
/**
 * A reaction may be an indication of an allergy or intolerance and, if this is determined to be the case, it should be recorded as a new AllergyIntolerance resource instance as most systems will not query against past Immunization.reaction elements.
 */
export interface ImmunizationReaction extends BackboneElement {
  /**
   * Date of reaction to the immunization.
   */
  date?: string;
  _date?: Element;
  /**
   * Details of the reaction.
   */
  detail?: Reference;
  /**
   * Self-reported indicator.
   */
  reported?: boolean;
  _reported?: Element;
}
/**
 * The protocol (set of recommendations) being followed by the provider who administered the dose.
 */
export interface ImmunizationProtocolApplied extends BackboneElement {
  /**
   * Indicates the authority who published the protocol (e.g. ACIP) that is being followed.
   */
  authority?: Reference;
  /**
   * The use of an integer is preferred if known. A string should only be used in cases where an integer is not available (such as when documenting a recurring booster dose).
   */
  doseNumberPositiveInt: number;
  /**
   * The use of an integer is preferred if known. A string should only be used in cases where an integer is not available (such as when documenting a recurring booster dose).
   */
  doseNumberString: string;
  _doseNumberString?: Element;
  /**
   * One possible path to achieve presumed immunity against a disease - within the context of an authority.
   */
  series?: string;
  _series?: Element;
  /**
   * The use of an integer is preferred if known. A string should only be used in cases where an integer is not available (such as when documenting a recurring booster dose).
   */
  seriesDosesPositiveInt?: number;
  /**
   * The use of an integer is preferred if known. A string should only be used in cases where an integer is not available (such as when documenting a recurring booster dose).
   */
  seriesDosesString?: string;
  _seriesDosesString?: Element;
  /**
   * The vaccine preventable disease the dose is being administered against.
   */
  targetDisease?: CodeableConcept[];
}
/**
 * Describes the event of a patient being administered a vaccine or a record of an immunization as reported by a patient, a clinician or another party.
 */
export interface Immunization extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Immunization';
  /**
   * The quantity of vaccine product that was administered.
   */
  doseQuantity?: Quantity;
  /**
   * Educational material presented to the patient (or guardian) at the time of vaccine administration.
   */
  education?: ImmunizationEducation[];
  /**
   * The visit or admission or other contact between patient and health care provider the immunization was performed as part of.
   */
  encounter?: Reference;
  /**
   * Date vaccine batch expires.
   */
  expirationDate?: string;
  _expirationDate?: Element;
  /**
   * Indicates the source of the vaccine actually administered. This may be different than the patient eligibility (e.g. the patient may be eligible for a publically purchased vaccine but due to inventory issues, vaccine purchased with private funds was actually administered).
   */
  fundingSource?: CodeableConcept;
  /**
   * A unique identifier assigned to this immunization record.
   */
  identifier?: Identifier[];
  /**
   * Typically, the recognition of the dose being sub-potent is retrospective, after the administration (ex. notification of a manufacturer recall after administration). However, in the case of a partial administration (the patient moves unexpectedly and only some of the dose is actually administered), subpotency may be recognized immediately, but it is still important to record the event.
   */
  isSubpotent?: boolean;
  _isSubpotent?: Element;
  /**
   * The service delivery location where the vaccine administration occurred.
   */
  location?: Reference;
  /**
   * Lot number of the  vaccine product.
   */
  lotNumber?: string;
  _lotNumber?: Element;
  /**
   * Name of vaccine manufacturer.
   */
  manufacturer?: Reference;
  /**
   * Extra information about the immunization that is not conveyed by the other attributes.
   */
  note?: Annotation[];
  /**
   * When immunizations are given a specific date and time should always be known.   When immunizations are patient reported, a specific date might not be known.  Although partial dates are allowed, an adult patient might not be able to recall the year a childhood immunization was given. An exact date is always preferable, but the use of the String data type is acceptable when an exact date is not known. A small number of vaccines (e.g. live oral typhoid vaccine) are given as a series of patient self-administered dose over a span of time. In cases like this, often, only the first dose (typically a provider supervised dose) is recorded with the occurrence indicating the date/time of the first dose.
   */
  occurrenceDateTime: string;
  _occurrenceDateTime?: Element;
  /**
   * When immunizations are given a specific date and time should always be known.   When immunizations are patient reported, a specific date might not be known.  Although partial dates are allowed, an adult patient might not be able to recall the year a childhood immunization was given. An exact date is always preferable, but the use of the String data type is acceptable when an exact date is not known. A small number of vaccines (e.g. live oral typhoid vaccine) are given as a series of patient self-administered dose over a span of time. In cases like this, often, only the first dose (typically a provider supervised dose) is recorded with the occurrence indicating the date/time of the first dose.
   */
  occurrenceString: string;
  _occurrenceString?: Element;
  /**
   * The patient who either received or did not receive the immunization.
   */
  patient: Reference;
  /**
   * Indicates who performed the immunization event.
   */
  performer?: ImmunizationPerformer[];
  /**
   * Reflects the “reliability” of the content.
   */
  primarySource?: boolean;
  _primarySource?: Element;
  /**
   * Indicates a patient's eligibility for a funding program.
   */
  programEligibility?: CodeableConcept[];
  /**
   * The protocol (set of recommendations) being followed by the provider who administered the dose.
   */
  protocolApplied?: ImmunizationProtocolApplied[];
  /**
   * A reaction may be an indication of an allergy or intolerance and, if this is determined to be the case, it should be recorded as a new AllergyIntolerance resource instance as most systems will not query against past Immunization.reaction elements.
   */
  reaction?: ImmunizationReaction[];
  /**
   * Reasons why the vaccine was administered.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Condition, Observation or DiagnosticReport that supports why the immunization was administered.
   */
  reasonReference?: Reference[];
  /**
   * The date the occurrence of the immunization was first captured in the record - potentially significantly after the occurrence of the event.
   */
  recorded?: string;
  _recorded?: Element;
  /**
   * Should not be populated if primarySource = True, not required even if primarySource = False.
   */
  reportOrigin?: CodeableConcept;
  /**
   * The path by which the vaccine product is taken into the body.
   */
  route?: CodeableConcept;
  /**
   * Body site where vaccine was administered.
   */
  site?: CodeableConcept;
  /**
   * Will generally be set to show that the immunization has been completed or not done.  This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * This is generally only used for the status of "not-done". The reason for performing the immunization event is captured in reasonCode, not here.
   */
  statusReason?: CodeableConcept;
  /**
   * Reason why a dose is considered to be subpotent.
   */
  subpotentReason?: CodeableConcept[];
  /**
   * Vaccine that was administered or was to be administered.
   */
  vaccineCode: CodeableConcept;
}
/**
 * Code Values for the Immunization.status field
 */
export enum ImmunizationStatusCodes {
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  NOT_DONE = 'not-done',
}
/**
 * Describes a comparison of an immunization event against published recommendations to determine if the administration is "valid" in relation to those  recommendations.
 */
export interface ImmunizationEvaluation extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ImmunizationEvaluation';
  /**
   * Indicates the authority who published the protocol (e.g. ACIP).
   */
  authority?: Reference;
  /**
   * The date the evaluation of the vaccine administration event was performed.
   */
  date?: string;
  _date?: Element;
  /**
   * Additional information about the evaluation.
   */
  description?: string;
  _description?: Element;
  /**
   * The use of an integer is preferred if known. A string should only be used in cases where an integer is not available (such as when documenting a recurring booster dose).
   */
  doseNumberPositiveInt?: number;
  /**
   * The use of an integer is preferred if known. A string should only be used in cases where an integer is not available (such as when documenting a recurring booster dose).
   */
  doseNumberString?: string;
  _doseNumberString?: Element;
  /**
   * Indicates if the dose is valid or not valid with respect to the published recommendations.
   */
  doseStatus: CodeableConcept;
  /**
   * Provides an explanation as to why the vaccine administration event is valid or not relative to the published recommendations.
   */
  doseStatusReason?: CodeableConcept[];
  /**
   * A unique identifier assigned to this immunization evaluation record.
   */
  identifier?: Identifier[];
  /**
   * The vaccine administration event being evaluated.
   */
  immunizationEvent: Reference;
  /**
   * The individual for whom the evaluation is being done.
   */
  patient: Reference;
  /**
   * One possible path to achieve presumed immunity against a disease - within the context of an authority.
   */
  series?: string;
  _series?: Element;
  /**
   * The use of an integer is preferred if known. A string should only be used in cases where an integer is not available (such as when documenting a recurring booster dose).
   */
  seriesDosesPositiveInt?: number;
  /**
   * The use of an integer is preferred if known. A string should only be used in cases where an integer is not available (such as when documenting a recurring booster dose).
   */
  seriesDosesString?: string;
  _seriesDosesString?: Element;
  /**
   * Indicates the current status of the evaluation of the vaccination administration event.
   */
  status: string;
  _status?: Element;
  /**
   * The vaccine preventable disease the dose is being evaluated against.
   */
  targetDisease: CodeableConcept;
}
/**
 * Code Values for the ImmunizationEvaluation.status field
 */
export enum ImmunizationEvaluationStatusCodes {
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Vaccine date recommendations.  For example, earliest date to administer, latest date to administer, etc.
 */
export interface ImmunizationRecommendationRecommendationDateCriterion extends BackboneElement {
  /**
   * Date classification of recommendation.  For example, earliest date to give, latest date to give, etc.
   */
  code: CodeableConcept;
  /**
   * The date whose meaning is specified by dateCriterion.code.
   */
  value: string;
  _value?: Element;
}
/**
 * Vaccine administration recommendations.
 */
export interface ImmunizationRecommendationRecommendation extends BackboneElement {
  /**
   * Vaccine(s) which should not be used to fulfill the recommendation.
   */
  contraindicatedVaccineCode?: CodeableConcept[];
  /**
   * Vaccine date recommendations.  For example, earliest date to administer, latest date to administer, etc.
   */
  dateCriterion?: ImmunizationRecommendationRecommendationDateCriterion[];
  /**
   * Contains the description about the protocol under which the vaccine was administered.
   */
  description?: string;
  _description?: Element;
  /**
   * The use of an integer is prefered if known. A string should only be used in cases where an interger is not available (such as when documenting a recurring booster dose).
   */
  doseNumberPositiveInt?: number;
  /**
   * The use of an integer is prefered if known. A string should only be used in cases where an interger is not available (such as when documenting a recurring booster dose).
   */
  doseNumberString?: string;
  _doseNumberString?: Element;
  /**
   * The reason for the assigned forecast status.
   */
  forecastReason?: CodeableConcept[];
  /**
   * Indicates the patient status with respect to the path to immunity for the target disease.
   */
  forecastStatus: CodeableConcept;
  /**
   * One possible path to achieve presumed immunity against a disease - within the context of an authority.
   */
  series?: string;
  _series?: Element;
  /**
   * The use of an integer is prefered if known. A string should only be used in cases where an interger is not available (such as when documenting a recurring booster dose).
   */
  seriesDosesPositiveInt?: number;
  /**
   * The use of an integer is prefered if known. A string should only be used in cases where an interger is not available (such as when documenting a recurring booster dose).
   */
  seriesDosesString?: string;
  _seriesDosesString?: Element;
  /**
   * Immunization event history and/or evaluation that supports the status and recommendation.
   */
  supportingImmunization?: Reference[];
  /**
   * Patient Information that supports the status and recommendation.  This includes patient observations, adverse reactions and allergy/intolerance information.
   */
  supportingPatientInformation?: Reference[];
  /**
   * The targeted disease for the recommendation.
   */
  targetDisease?: CodeableConcept;
  /**
   * Vaccine(s) or vaccine group that pertain to the recommendation.
   */
  vaccineCode?: CodeableConcept[];
}
/**
 * A patient's point-in-time set of recommendations (i.e. forecasting) according to a published schedule with optional supporting justification.
 */
export interface ImmunizationRecommendation extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ImmunizationRecommendation';
  /**
   * Indicates the authority who published the protocol (e.g. ACIP).
   */
  authority?: Reference;
  /**
   * The date the immunization recommendation(s) were created.
   */
  date: string;
  _date?: Element;
  /**
   * A unique identifier assigned to this particular recommendation record.
   */
  identifier?: Identifier[];
  /**
   * The patient the recommendation(s) are for.
   */
  patient: Reference;
  /**
   * Vaccine administration recommendations.
   */
  recommendation: ImmunizationRecommendationRecommendation[];
}
/**
 * Another implementation guide that this implementation depends on. Typically, an implementation guide uses value sets, profiles etc.defined in other implementation guides.
 */
export interface ImplementationGuideDependsOn extends BackboneElement {
  /**
   * The NPM package name for the Implementation Guide that this IG depends on.
   */
  packageId?: string;
  _packageId?: Element;
  /**
   * Usually, A canonical reference to the implementation guide is the same as the master location at which the implementation guide is published.
   */
  uri: string;
  _uri?: Element;
  /**
   * This follows the syntax of the NPM packaging version field - see [[reference]].
   */
  version?: string;
  _version?: Element;
}
/**
 * See [Default Profiles](implementationguide.html#default) for a discussion of which resources are 'covered' by an implementation guide.
 */
export interface ImplementationGuideGlobal extends BackboneElement {
  /**
   * A reference to the profile that all instances must conform to.
   */
  profile: string;
  _profile?: Element;
  /**
   * The type must match that of the profile that is referred to but is made explicit here as a denormalization so that a system processing the implementation guide resource knows which resources the profile applies to even if the profile itself is not available.
   */
  type: string;
  _type?: Element;
}
/**
 * Groupings are arbitrary sub-divisions of content. Typically, they are used to help build Table of Contents automatically.
 */
export interface ImplementationGuideDefinitionGrouping extends BackboneElement {
  /**
   * Human readable text describing the package.
   */
  description?: string;
  _description?: Element;
  /**
   * The human-readable title to display for the package of resources when rendering the implementation guide.
   */
  name: string;
  _name?: Element;
}
/**
 * A resource that is part of the implementation guide. Conformance resources (value set, structure definition, capability statements etc.) are obvious candidates for inclusion, but any kind of resource can be included as an example resource.
 */
export interface ImplementationGuideDefinitionResource extends BackboneElement {
  /**
   * This is mostly used with examples to explain why it is present (though they can have extensive comments in the examples).
   */
  description?: string;
  _description?: Element;
  /**
   * Examples:
   * * StructureDefinition -> Any
   * * ValueSet -> expansion
   * * OperationDefinition -> Parameters
   * * Questionnaire -> QuestionnaireResponse.
   */
  exampleBoolean?: boolean;
  _exampleBoolean?: Element;
  /**
   * Examples:
   * * StructureDefinition -> Any
   * * ValueSet -> expansion
   * * OperationDefinition -> Parameters
   * * Questionnaire -> QuestionnaireResponse.
   */
  exampleCanonical?: string;
  _exampleCanonical?: Element;
  /**
   * The resource SHALL be valid against all the versions it is specified to apply to. If the resource referred to is a StructureDefinition, the fhirVersion stated in the StructureDefinition cannot disagree with the version specified here; the specified versions SHALL include the version specified by the StructureDefinition, and may include additional versions using the [applicable-version](extension-structuredefinition-applicable-version.html) extension.
   */
  fhirVersion?: string[];
  _fhirVersion?: Element;
  /**
   * This must correspond to a package.id element within this implementation guide.
   */
  groupingId?: string;
  _groupingId?: Element;
  /**
   * A human assigned name for the resource. All resources SHOULD have a name, but the name may be extracted from the resource (e.g. ValueSet.name).
   */
  name?: string;
  _name?: Element;
  /**
   * Usually this is a relative URL that locates the resource within the implementation guide. If you authoring an implementation guide, and will publish it using the FHIR publication tooling, use a URI that may point to a resource, or to one of various alternative representations (e.g. spreadsheet). The tooling will convert this when it publishes it.
   */
  reference: Reference;
}
/**
 * Pages automatically become sections if they have sub-pages. By convention, the home page is called index.html.
 */
export interface ImplementationGuideDefinitionPage extends BackboneElement {
  /**
   * A code that indicates how the page is generated.
   */
  generation: string;
  _generation?: Element;
  /**
   * The publishing tool will autogenerate source for list (source = n/a) and inject included implementations for include (source = uri of guide to include).
   */
  nameUrl: string;
  _nameUrl?: Element;
  /**
   * The publishing tool will autogenerate source for list (source = n/a) and inject included implementations for include (source = uri of guide to include).
   */
  nameReference: Reference;
  /**
   * The implementation guide breadcrumbs are generated from this structure.
   */
  page?: ImplementationGuideDefinitionPage[];
  /**
   * A short title used to represent this page in navigational structures such as table of contents, bread crumbs, etc.
   */
  title: string;
  _title?: Element;
}
/**
 * Code Values for the ImplementationGuide.definition.page.generation field
 */
export enum ImplementationGuideDefinitionPageGenerationCodes {
  HTML = 'html',
  MARKDOWN = 'markdown',
  XML = 'xml',
  GENERATED = 'generated',
}
/**
 * Defines how IG is built by tools.
 */
export interface ImplementationGuideDefinitionParameter extends BackboneElement {
  /**
   * apply | path-resource | path-pages | path-tx-cache | expansion-parameter | rule-broken-links | generate-xml | generate-json | generate-turtle | html-template.
   */
  code: string;
  _code?: Element;
  /**
   * Value for named type.
   */
  value: string;
  _value?: Element;
}
/**
 * Code Values for the ImplementationGuide.definition.parameter.code field
 */
export enum ImplementationGuideDefinitionParameterCodeCodes {
  APPLY = 'apply',
  PATH_RESOURCE = 'path-resource',
  PATH_PAGES = 'path-pages',
  PATH_TX_CACHE = 'path-tx-cache',
  EXPANSION_PARAMETER = 'expansion-parameter',
  RULE_BROKEN_LINKS = 'rule-broken-links',
  GENERATE_XML = 'generate-xml',
  GENERATE_JSON = 'generate-json',
  GENERATE_TURTLE = 'generate-turtle',
  HTML_TEMPLATE = 'html-template',
}
/**
 * A template for building resources.
 */
export interface ImplementationGuideDefinitionTemplate extends BackboneElement {
  /**
   * Type of template specified.
   */
  code: string;
  _code?: Element;
  /**
   * The scope in which the template applies.
   */
  scope?: string;
  _scope?: Element;
  /**
   * The source location for the template.
   */
  source: string;
  _source?: Element;
}
/**
 * Principally, this consists of information abuot source resource and file locations, and build parameters and templates.
 */
export interface ImplementationGuideDefinition extends BackboneElement {
  /**
   * Groupings are arbitrary sub-divisions of content. Typically, they are used to help build Table of Contents automatically.
   */
  grouping?: ImplementationGuideDefinitionGrouping[];
  /**
   * Pages automatically become sections if they have sub-pages. By convention, the home page is called index.html.
   */
  page?: ImplementationGuideDefinitionPage;
  /**
   * Defines how IG is built by tools.
   */
  parameter?: ImplementationGuideDefinitionParameter[];
  /**
   * A resource that is part of the implementation guide. Conformance resources (value set, structure definition, capability statements etc.) are obvious candidates for inclusion, but any kind of resource can be included as an example resource.
   */
  resource: ImplementationGuideDefinitionResource[];
  /**
   * A template for building resources.
   */
  template?: ImplementationGuideDefinitionTemplate[];
}
/**
 * A resource that is part of the implementation guide. Conformance resources (value set, structure definition, capability statements etc.) are obvious candidates for inclusion, but any kind of resource can be included as an example resource.
 */
export interface ImplementationGuideManifestResource extends BackboneElement {
  /**
   * Typically, conformance resources and knowledge resources are directly part of the implementation guide, with their normal meaning, and patient linked resources are usually examples. However this is not always true.
   */
  exampleBoolean?: boolean;
  _exampleBoolean?: Element;
  /**
   * Typically, conformance resources and knowledge resources are directly part of the implementation guide, with their normal meaning, and patient linked resources are usually examples. However this is not always true.
   */
  exampleCanonical?: string;
  _exampleCanonical?: Element;
  /**
   * Usually this is a relative URL that locates the resource within the implementation guide. If you authoring an implementation guide, and will publish it using the FHIR publication tooling, use a URI that may point to a resource, or to one of various alternative representations (e.g. spreadsheet). The tooling will convert this when it publishes it.
   */
  reference: Reference;
  /**
   * Appending 'rendering' + "/" + this should resolve to the resource page.
   */
  relativePath?: string;
  _relativePath?: Element;
}
/**
 * Information about a page within the IG.
 */
export interface ImplementationGuideManifestPage extends BackboneElement {
  /**
   * Appending 'rendering' + "/" + page.name + "#" + page.anchor should resolve to the anchor.
   */
  anchor?: string[];
  _anchor?: Element;
  /**
   * Appending 'rendering' + "/" + this should resolve to the page.
   */
  name: string;
  _name?: Element;
  /**
   * Label for the page intended for human display.
   */
  title?: string;
  _title?: Element;
}
/**
 * Information about an assembled implementation guide, created by the publication tooling.
 */
export interface ImplementationGuideManifest extends BackboneElement {
  /**
   * Indicates a relative path to an image that exists within the IG.
   */
  image?: string[];
  _image?: Element;
  /**
   * Indicates the relative path of an additional non-page, non-image file that is part of the IG - e.g. zip, jar and similar files that could be the target of a hyperlink in a derived IG.
   */
  other?: string[];
  _other?: Element;
  /**
   * Information about a page within the IG.
   */
  page?: ImplementationGuideManifestPage[];
  /**
   * A pointer to official web page, PDF or other rendering of the implementation guide.
   */
  rendering?: string;
  _rendering?: Element;
  /**
   * A resource that is part of the implementation guide. Conformance resources (value set, structure definition, capability statements etc.) are obvious candidates for inclusion, but any kind of resource can be included as an example resource.
   */
  resource: ImplementationGuideManifestResource[];
}
/**
 * A set of rules of how a particular interoperability or standards problem is solved - typically through the use of FHIR resources. This resource is used to gather all the parts of an implementation guide into a logical whole and to publish a computable definition of all the parts.
 */
export interface ImplementationGuide extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ImplementationGuide';
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the implementation guide and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the implementation guide.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the implementation guide. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * Principally, this consists of information abuot source resource and file locations, and build parameters and templates.
   */
  definition?: ImplementationGuideDefinition;
  /**
   * Another implementation guide that this implementation depends on. Typically, an implementation guide uses value sets, profiles etc.defined in other implementation guides.
   */
  dependsOn?: ImplementationGuideDependsOn[];
  /**
   * This description can be used to capture details such as why the implementation guide was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the implementation guide as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the implementation guide is presumed to be the predominant language in the place the implementation guide was created).
   */
  description?: string;
  _description?: Element;
  /**
   * Allows filtering of implementation guides that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Most implementation guides target a single version - e.g. they describe how to use a particular version, and the profiles and examples etc are valid for that version. But some implementation guides describe how to use multiple different versions of FHIR to solve the same problem, or in concert with each other. Typically, the requirement to support multiple versions arises as implementation matures and different implementation communities are stuck at different versions by regulation or market dynamics.
   */
  fhirVersion: string[];
  _fhirVersion?: Element;
  /**
   * See [Default Profiles](implementationguide.html#default) for a discussion of which resources are 'covered' by an implementation guide.
   */
  global?: ImplementationGuideGlobal[];
  /**
   * It may be possible for the implementation guide to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * The license that applies to this Implementation Guide, using an SPDX license code, or 'not-open-source'.
   */
  license?: string;
  _license?: Element;
  /**
   * Information about an assembled implementation guide, created by the publication tooling.
   */
  manifest?: ImplementationGuideManifest;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name: string;
  _name?: Element;
  /**
   * Many (if not all) IG publishing tools will require that this element be present. For implementation guides published through HL7 or the FHIR foundation, the FHIR product director assigns package IDs.
   */
  packageId: string;
  _packageId?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the implementation guide is the organization or individual primarily responsible for the maintenance and upkeep of the implementation guide. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the implementation guide. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * Allows filtering of implementation guides that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different implementation guide instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the implementation guide with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the ImplementationGuide.status field
 */
export enum ImplementationGuideStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Where multiple contacts for the same purpose are provided there is a standard extension that can be used to determine which one is the preferred contact to use.
 */
export interface InsurancePlanContact extends BackboneElement {
  /**
   * Visiting or postal addresses for the contact.
   */
  address?: Address;
  /**
   * A name associated with the contact.
   */
  name?: HumanName;
  /**
   * Indicates a purpose for which the contact can be reached.
   */
  purpose?: CodeableConcept;
  /**
   * A contact detail (e.g. a telephone number or an email address) by which the party may be contacted.
   */
  telecom?: ContactPoint[];
}
/**
 * The specific limits on the benefit.
 */
export interface InsurancePlanCoverageBenefitLimit extends BackboneElement {
  /**
   * Use `CodeableConcept.text` element if the data is free (uncoded) text.
   */
  code?: CodeableConcept;
  /**
   * May also be called “eligible expense,” “payment allowance,” or “negotiated rate.”.
   */
  value?: Quantity;
}
/**
 * Specific benefits under this type of coverage.
 */
export interface InsurancePlanCoverageBenefit extends BackboneElement {
  /**
   * The specific limits on the benefit.
   */
  limit?: InsurancePlanCoverageBenefitLimit[];
  /**
   * The referral requirements to have access/coverage for this benefit.
   */
  requirement?: string;
  _requirement?: Element;
  /**
   * Type of benefit (primary care; speciality care; inpatient; outpatient).
   */
  type: CodeableConcept;
}
/**
 * Details about the coverage offered by the insurance product.
 */
export interface InsurancePlanCoverage extends BackboneElement {
  /**
   * Specific benefits under this type of coverage.
   */
  benefit: InsurancePlanCoverageBenefit[];
  /**
   * Networks are represented as a hierarchy of organization resources.
   */
  network?: Reference[];
  /**
   * Type of coverage  (Medical; Dental; Mental Health; Substance Abuse; Vision; Drug; Short Term; Long Term Care; Hospice; Home Health).
   */
  type: CodeableConcept;
}
/**
 * Overall costs associated with the plan.
 */
export interface InsurancePlanPlanGeneralCost extends BackboneElement {
  /**
   * Additional information about the general costs associated with this plan.
   */
  comment?: string;
  _comment?: Element;
  /**
   * Value of the cost.
   */
  cost?: Money;
  /**
   * Number of participants enrolled in the plan.
   */
  groupSize?: number;
  /**
   * Type of cost.
   */
  type?: CodeableConcept;
}
/**
 * List of the costs associated with a specific benefit.
 */
export interface InsurancePlanPlanSpecificCostBenefitCost extends BackboneElement {
  /**
   * Whether the cost applies to in-network or out-of-network providers (in-network; out-of-network; other).
   */
  applicability?: CodeableConcept;
  /**
   * Additional information about the cost, such as information about funding sources (e.g. HSA, HRA, FSA, RRA).
   */
  qualifiers?: CodeableConcept[];
  /**
   * Type of cost (copay; individual cap; family cap; coinsurance; deductible).
   */
  type: CodeableConcept;
  /**
   * The actual cost value. (some of the costs may be represented as percentages rather than currency, e.g. 10% coinsurance).
   */
  value?: Quantity;
}
/**
 * List of the specific benefits under this category of benefit.
 */
export interface InsurancePlanPlanSpecificCostBenefit extends BackboneElement {
  /**
   * List of the costs associated with a specific benefit.
   */
  cost?: InsurancePlanPlanSpecificCostBenefitCost[];
  /**
   * Type of specific benefit (preventative; primary care office visit; speciality office visit; hospitalization; emergency room; urgent care).
   */
  type: CodeableConcept;
}
/**
 * Costs associated with the coverage provided by the product.
 */
export interface InsurancePlanPlanSpecificCost extends BackboneElement {
  /**
   * List of the specific benefits under this category of benefit.
   */
  benefit?: InsurancePlanPlanSpecificCostBenefit[];
  /**
   * General category of benefit (Medical; Dental; Vision; Drug; Mental Health; Substance Abuse; Hospice, Home Health).
   */
  category: CodeableConcept;
}
/**
 * Details about an insurance plan.
 */
export interface InsurancePlanPlan extends BackboneElement {
  /**
   * The geographic region in which a health insurance plan's benefits apply.
   */
  coverageArea?: Reference[];
  /**
   * Overall costs associated with the plan.
   */
  generalCost?: InsurancePlanPlanGeneralCost[];
  /**
   * Business identifiers assigned to this health insurance plan which remain constant as the resource is updated and propagates from server to server.
   */
  identifier?: Identifier[];
  /**
   * Networks are represented as a hierarchy of organization resources.
   */
  network?: Reference[];
  /**
   * Costs associated with the coverage provided by the product.
   */
  specificCost?: InsurancePlanPlanSpecificCost[];
  /**
   * Type of plan. For example, "Platinum" or "High Deductable".
   */
  type?: CodeableConcept;
}
/**
 * Details of a Health Insurance product/plan provided by an organization.
 */
export interface InsurancePlan extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'InsurancePlan';
  /**
   * An organization which administer other services such as underwriting, customer service and/or claims processing on behalf of the health insurance product owner.
   */
  administeredBy?: Reference;
  /**
   * There are no dates associated with the alias/historic names, as this is not intended to track when names were used, but to assist in searching so that older names can still result in identifying the product/plan.
   */
  alias?: string[];
  _alias?: Element;
  /**
   * Where multiple contacts for the same purpose are provided there is a standard extension that can be used to determine which one is the preferred contact to use.
   */
  contact?: InsurancePlanContact[];
  /**
   * Details about the coverage offered by the insurance product.
   */
  coverage?: InsurancePlanCoverage[];
  /**
   * The geographic region in which a health insurance product's benefits apply.
   */
  coverageArea?: Reference[];
  /**
   * The technical endpoints providing access to services operated for the health insurance product.
   */
  endpoint?: Reference[];
  /**
   * Business identifiers assigned to this health insurance product which remain constant as the resource is updated and propagates from server to server.
   */
  identifier?: Identifier[];
  /**
   * If the name of the product/plan changes, consider putting the old name in the alias column so that it can still be located through searches.
   */
  name?: string;
  _name?: Element;
  /**
   * Networks are represented as a hierarchy of organization resources.
   */
  network?: Reference[];
  /**
   * The entity that is providing  the health insurance product and underwriting the risk.  This is typically an insurance carriers, other third-party payers, or health plan sponsors comonly referred to as 'payers'.
   */
  ownedBy?: Reference;
  /**
   * The period of time that the health insurance product is available.
   */
  period?: Period;
  /**
   * Details about an insurance plan.
   */
  plan?: InsurancePlanPlan[];
  /**
   * The current state of the health insurance product.
   */
  status?: string;
  _status?: Element;
  /**
   * The kind of health insurance product.
   */
  type?: CodeableConcept[];
}
/**
 * Code Values for the InsurancePlan.status field
 */
export enum InsurancePlanStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Indicates who or what performed or participated in the charged service.
 */
export interface InvoiceParticipant extends BackboneElement {
  /**
   * The device, practitioner, etc. who performed or participated in the service.
   */
  actor: Reference;
  /**
   * Describes the type of involvement (e.g. transcriptionist, creator etc.). If the invoice has been created automatically, the Participant may be a billing engine or another kind of device.
   */
  role?: CodeableConcept;
}
/**
 * The price for a ChargeItem may be calculated as a base price with surcharges/deductions that apply in certain conditions. A ChargeItemDefinition resource that defines the prices, factors and conditions that apply to a billing code is currently under development. The priceComponent element can be used to offer transparency to the recipient of the Invoice as to how the prices have been calculated.
 */
export interface InvoiceLineItemPriceComponent extends BackboneElement {
  /**
   * There is no reason to carry the price in the instance of a ChargeItem unless circumstances require a manual override. The list prices or are usually defined in a back catalogue of the billing codes  (see ChargeItem.definition). Derived profiles may require a ChargeItem.overrideReason to be provided if either factor or price are manually overridden.
   */
  amount?: Money;
  /**
   * A code that identifies the component. Codes may be used to differentiate between kinds of taxes, surcharges, discounts etc.
   */
  code?: CodeableConcept;
  /**
   * There is no reason to carry the price in the instance of a ChargeItem unless circumstances require a manual override. The list prices or are usually defined in a back catalogue of the billing codes  (see ChargeItem.definition). Derived profiles may require a ChargeItem.overrideReason to be provided if either factor or price are manually overridden.
   */
  factor?: number;
  /**
   * This code identifies the type of the component.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the Invoice.lineItem.priceComponent.type field
 */
export enum InvoiceLineItemPriceComponentTypeCodes {
  BASE = 'base',
  SURCHARGE = 'surcharge',
  DEDUCTION = 'deduction',
  DISCOUNT = 'discount',
  TAX = 'tax',
  INFORMATIONAL = 'informational',
}
/**
 * Each line item represents one charge for goods and services rendered. Details such as date, code and amount are found in the referenced ChargeItem resource.
 */
export interface InvoiceLineItem extends BackboneElement {
  /**
   * The ChargeItem contains information such as the billing code, date, amount etc. If no further details are required for the lineItem, inline billing codes can be added using the CodeableConcept data type instead of the Reference.
   */
  chargeItemReference: Reference;
  /**
   * The ChargeItem contains information such as the billing code, date, amount etc. If no further details are required for the lineItem, inline billing codes can be added using the CodeableConcept data type instead of the Reference.
   */
  chargeItemCodeableConcept: CodeableConcept;
  /**
   * The price for a ChargeItem may be calculated as a base price with surcharges/deductions that apply in certain conditions. A ChargeItemDefinition resource that defines the prices, factors and conditions that apply to a billing code is currently under development. The priceComponent element can be used to offer transparency to the recipient of the Invoice as to how the prices have been calculated.
   */
  priceComponent?: InvoiceLineItemPriceComponent[];
  /**
   * Sequence in which the items appear on the invoice.
   */
  sequence?: number;
}
/**
 * Invoice containing collected ChargeItems from an Account with calculated individual and total price for Billing purpose.
 */
export interface Invoice extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Invoice';
  /**
   * Systems posting the ChargeItems might not always be able to determine, which accounts the Items need to be places into. It is up to the potprocessing Financial System to apply internal rules to decide based on the Encounter/EpisodeOfCare/Patient/Coverage context and the type of ChargeItem, which Account is appropriate.
   */
  account?: Reference;
  /**
   * Derived Profiles may choose to add invariants requiring this field to be populated if either priceOverride or factorOverride have been filled.
   */
  cancelledReason?: string;
  _cancelledReason?: Element;
  /**
   * The list of types may be constrained as appropriate for the type of charge item.
   */
  date?: string;
  _date?: Element;
  /**
   * Identifier of this Invoice, often used for reference in correspondence about this invoice or for tracking of payments.
   */
  identifier?: Identifier[];
  /**
   * Practitioners and Devices can be associated with multiple organizations. It has to be made clear, on behalf of which Organization the services have been rendered.
   */
  issuer?: Reference;
  /**
   * Each line item represents one charge for goods and services rendered. Details such as date, code and amount are found in the referenced ChargeItem resource.
   */
  lineItem?: InvoiceLineItem[];
  /**
   * Comments made about the invoice by the issuer, subject, or other participants.
   */
  note?: Annotation[];
  /**
   * Indicates who or what performed or participated in the charged service.
   */
  participant?: InvoiceParticipant[];
  /**
   * Derived Profiles may chose to add invariants requiring this field to be populated if either priceOverride or factorOverride have been filled.
   */
  paymentTerms?: string;
  _paymentTerms?: Element;
  /**
   * The individual or Organization responsible for balancing of this invoice.
   */
  recipient?: Reference;
  /**
   * The current state of the Invoice.
   */
  status: string;
  _status?: Element;
  /**
   * The individual or set of individuals receiving the goods and services billed in this invoice.
   */
  subject?: Reference;
  /**
   * There is no reason to carry the price in the instance of a ChargeItem unless circumstances require a manual override. The list prices or are usually defined in a back catalogue of the billing codes  (see ChargeItem.definition). Derived profiles may require a ChargeItem.overrideReason to be provided if either factor or price are manually overridden.
   */
  totalGross?: Money;
  /**
   * There is no reason to carry the price in the instance of a ChargeItem unless circumstances require a manual override. The list prices or are usually defined in a back catalogue of the billing codes  (see ChargeItem.definition). Derived profiles may require a ChargeItem.overrideReason to be provided if either factor or price are manually overridden.
   */
  totalNet?: Money;
  /**
   * The total amount for the Invoice may be calculated as the sum of the line items with surcharges/deductions that apply in certain conditions.  The priceComponent element can be used to offer transparency to the recipient of the Invoice of how the total price was calculated.
   */
  totalPriceComponent?: InvoiceLineItemPriceComponent[];
  /**
   * Type of Invoice depending on domain, realm an usage (e.g. internal/external, dental, preliminary).
   */
  type?: CodeableConcept;
}
/**
 * Code Values for the Invoice.status field
 */
export enum InvoiceStatusCodes {
  DRAFT = 'draft',
  ISSUED = 'issued',
  BALANCED = 'balanced',
  CANCELLED = 'cancelled',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * The Library resource is a general-purpose container for knowledge asset definitions. It can be used to describe and expose existing knowledge assets such as logic libraries and information model descriptions, as well as to describe a collection of knowledge assets.
 */
export interface Library extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Library';
  /**
   * The 'date' element may be more recent than the approval date because of minor changes or editorial corrections.
   */
  approvalDate?: string;
  _approvalDate?: Element;
  /**
   * An individiual or organization primarily involved in the creation and maintenance of the content.
   */
  author?: ContactDetail[];
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * The content of the library as an Attachment. The content may be a reference to a url, or may be directly embedded as a base-64 string. Either way, the contentType of the attachment determines how to interpret the content.
   */
  content?: Attachment[];
  /**
   * A copyright statement relating to the library and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the library.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Describes a set of data that must be provided in order to be able to successfully perform the computations defined by the library.
   */
  dataRequirement?: DataRequirement[];
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the library. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the library was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the library as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the library is presumed to be the predominant language in the place the library was created).
   */
  description?: string;
  _description?: Element;
  /**
   * An individual or organization primarily responsible for internal coherence of the content.
   */
  editor?: ContactDetail[];
  /**
   * The effective period for a library  determines when the content is applicable for usage and is independent of publication and review dates. For example, a measure intended to be used for the year 2016 might be published in 2015.
   */
  effectivePeriod?: Period;
  /**
   * An individual or organization responsible for officially endorsing the content for use in some setting.
   */
  endorser?: ContactDetail[];
  /**
   * Allows filtering of librarys that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this library outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * It may be possible for the library to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * If specified, this date follows the original approval date.
   */
  lastReviewDate?: string;
  _lastReviewDate?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * The parameter element defines parameters used by the library.
   */
  parameter?: ParameterDefinition[];
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the library is the organization or individual primarily responsible for the maintenance and upkeep of the library. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the library. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the library. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this library.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Each related artifact is either an attachment, or a reference to another resource, but not both.
   */
  relatedArtifact?: RelatedArtifact[];
  /**
   * An individual or organization primarily responsible for review of some aspect of the content.
   */
  reviewer?: ContactDetail[];
  /**
   * Allows filtering of libraries that are appropriate for use vs. not.
   */
  status: string;
  _status?: Element;
  /**
   * A code or group definition that describes the intended subject of the contents of the library.
   */
  subjectCodeableConcept?: CodeableConcept;
  /**
   * A code or group definition that describes the intended subject of the contents of the library.
   */
  subjectReference?: Reference;
  /**
   * An explanatory or alternate title for the library giving additional information about its content.
   */
  subtitle?: string;
  _subtitle?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Descriptive topics related to the content of the library. Topics provide a high-level categorization of the library that can be useful for filtering and searching.
   */
  topic?: CodeableConcept[];
  /**
   * Identifies the type of library such as a Logic Library, Model Definition, Asset Collection, or Module Definition.
   */
  type: CodeableConcept;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * A detailed description of how the library is used from a clinical perspective.
   */
  usage?: string;
  _usage?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different library instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the library with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the Library.status field
 */
export enum LibraryStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Identifies which record considered as the reference to the same real-world occurrence as well as how the items should be evaluated within the collection of linked items.
 */
export interface LinkageItem extends BackboneElement {
  /**
   * The resource instance being linked as part of the group.
   */
  resource: Reference;
  /**
   * Distinguishes which item is "source of truth" (if any) and which items are no longer considered to be current representations.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the Linkage.item.type field
 */
export enum LinkageItemTypeCodes {
  SOURCE = 'source',
  ALTERNATE = 'alternate',
  HISTORICAL = 'historical',
}
/**
 * Identifies two or more records (resource instances) that refer to the same real-world "occurrence".
 */
export interface Linkage extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Linkage';
  /**
   * If false, any asserted linkages should not be considered current/relevant/applicable.
   */
  active?: boolean;
  _active?: Element;
  /**
   * Identifies the user or organization responsible for asserting the linkages as well as the user or organization who establishes the context in which the nature of each linkage is evaluated.
   */
  author?: Reference;
  /**
   * Identifies which record considered as the reference to the same real-world occurrence as well as how the items should be evaluated within the collection of linked items.
   */
  item: LinkageItem[];
}
/**
 * If there are no entries in the list, an emptyReason SHOULD be provided.
 */
export interface ListEntry extends BackboneElement {
  /**
   * When this item was added to the list.
   */
  date?: string;
  _date?: Element;
  /**
   * If the flag means that the entry has actually been deleted from the list, the deleted element SHALL be true. Both flag and deleted can only be used if the List.mode is "changes". A deleted entry should be displayed in narrative as deleted.  This element is labeled as a modifier because it indicates that an item is (to be) no longer in the list.
   */
  deleted?: boolean;
  _deleted?: Element;
  /**
   * The flag can only be understood in the context of the List.code. If the flag means that the entry has actually been deleted from the list, the deleted element SHALL be true. Deleted can only be used if the List.mode is "changes".
   */
  flag?: CodeableConcept;
  /**
   * A reference to the actual resource from which data was derived.
   */
  item: Reference;
}
/**
 * A list is a curated collection of resources.
 */
export interface List extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'List';
  /**
   * If there is no code, the purpose of the list is implied where it is used, such as in a document section using Document.section.code.
   */
  code?: CodeableConcept;
  /**
   * The actual important date is the date of currency of the resources that were summarized, but it is usually assumed that these are current when the preparation occurs.
   */
  date?: string;
  _date?: Element;
  /**
   * The various reasons for an empty list make a significant interpretation to its interpretation. Note that this code is for use when the entire list has been suppressed, and not for when individual items are omitted - implementers may consider using a text note or a flag on an entry in these cases.
   */
  emptyReason?: CodeableConcept;
  /**
   * The encounter that is the context in which this list was created.
   */
  encounter?: Reference;
  /**
   * If there are no entries in the list, an emptyReason SHOULD be provided.
   */
  entry?: ListEntry[];
  /**
   * Identifier for the List assigned for business purposes outside the context of FHIR.
   */
  identifier?: Identifier[];
  /**
   * This element is labeled as a modifier because a change list must not be misunderstood as a complete list.
   */
  mode: string;
  _mode?: Element;
  /**
   * Comments that apply to the overall list.
   */
  note?: Annotation[];
  /**
   * Applications SHOULD render ordered lists in the order provided, but MAY allow users to re-order based on their own preferences as well. If there is no order specified, the order is unknown, though there may still be some order.
   */
  orderedBy?: CodeableConcept;
  /**
   * The primary source is the entity that made the decisions what items are in the list. This may be software or user.
   */
  source?: Reference;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * Some purely arbitrary lists do not have a common subject, so this is optional.
   */
  subject?: Reference;
  /**
   * A label for the list assigned by the author.
   */
  title?: string;
  _title?: Element;
}
/**
 * Code Values for the List.mode field
 */
export enum ListModeCodes {
  WORKING = 'working',
  SNAPSHOT = 'snapshot',
  CHANGES = 'changes',
}
/**
 * Code Values for the List.status field
 */
export enum ListStatusCodes {
  CURRENT = 'current',
  RETIRED = 'retired',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * The absolute geographic location of the Location, expressed using the WGS84 datum (This is the same co-ordinate system used in KML).
 */
export interface LocationPosition extends BackboneElement {
  /**
   * Altitude. The value domain and the interpretation are the same as for the text of the altitude element in KML (see notes below).
   */
  altitude?: number;
  /**
   * Latitude. The value domain and the interpretation are the same as for the text of the latitude element in KML (see notes below).
   */
  latitude: number;
  /**
   * Longitude. The value domain and the interpretation are the same as for the text of the longitude element in KML (see notes below).
   */
  longitude: number;
}
/**
 * This type of information is commonly found published in directories and on websites informing customers when the facility is available.
 * Specific services within the location may have their own hours which could be shorter (or longer) than the locations hours.
 */
export interface LocationHoursOfOperation extends BackboneElement {
  /**
   * The Location is open all day.
   */
  allDay?: boolean;
  _allDay?: Element;
  /**
   * Time that the Location closes.
   */
  closingTime?: string;
  _closingTime?: Element;
  /**
   * Indicates which days of the week are available between the start and end Times.
   */
  daysOfWeek?: string[];
  _daysOfWeek?: Element;
  /**
   * Time that the Location opens.
   */
  openingTime?: string;
  _openingTime?: Element;
}
/**
 * Code Values for the Location.hoursOfOperation.daysOfWeek field
 */
export enum LocationHoursOfOperationDaysOfWeekCodes {
  MON = 'mon',
  TUE = 'tue',
  WED = 'wed',
  THU = 'thu',
  FRI = 'fri',
  SAT = 'sat',
  SUN = 'sun',
}
/**
 * Details and position information for a physical place where services are provided and resources and participants may be stored, found, contained, or accommodated.
 */
export interface Location extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Location';
  /**
   * Additional addresses should be recorded using another instance of the Location resource, or via the Organization.
   */
  address?: Address;
  /**
   * There are no dates associated with the alias/historic names, as this is not intended to track when names were used, but to assist in searching so that older names can still result in identifying the location.
   */
  alias?: string[];
  _alias?: Element;
  /**
   * A description of when the locations opening ours are different to normal, e.g. public holiday availability. Succinctly describing all possible exceptions to normal site availability as detailed in the opening hours Times.
   */
  availabilityExceptions?: string;
  _availabilityExceptions?: Element;
  /**
   * Description of the Location, which helps in finding or referencing the place.
   */
  description?: string;
  _description?: Element;
  /**
   * Technical endpoints providing access to services operated for the location.
   */
  endpoint?: Reference[];
  /**
   * This type of information is commonly found published in directories and on websites informing customers when the facility is available.
   * Specific services within the location may have their own hours which could be shorter (or longer) than the locations hours.
   */
  hoursOfOperation?: LocationHoursOfOperation[];
  /**
   * Unique code or number identifying the location to its users.
   */
  identifier?: Identifier[];
  /**
   * This can also be used as the part of the organization hierarchy where this location provides services. These services can be defined through the HealthcareService resource.
   */
  managingOrganization?: Reference;
  /**
   * This is labeled as a modifier because whether or not the location is a class of locations changes how it can be used and understood.
   */
  mode?: string;
  _mode?: Element;
  /**
   * If the name of a location changes, consider putting the old name in the alias column so that it can still be located through searches.
   */
  name?: string;
  _name?: Element;
  /**
   * The operational status covers operation values most relevant to beds (but can also apply to rooms/units/chairs/etc. such as an isolation unit/dialysis chair). This typically covers concepts such as contamination, housekeeping, and other activities like maintenance.
   */
  operationalStatus?: Coding;
  /**
   * Another Location of which this Location is physically a part of.
   */
  partOf?: Reference;
  /**
   * Physical form of the location, e.g. building, room, vehicle, road.
   */
  physicalType?: CodeableConcept;
  /**
   * The absolute geographic location of the Location, expressed using the WGS84 datum (This is the same co-ordinate system used in KML).
   */
  position?: LocationPosition;
  /**
   * The status property covers the general availability of the resource, not the current value which may be covered by the operationStatus, or by a schedule/slots if they are configured for the location.
   */
  status?: string;
  _status?: Element;
  /**
   * The contact details of communication devices available at the location. This can include phone numbers, fax numbers, mobile numbers, email addresses and web sites.
   */
  telecom?: ContactPoint[];
  /**
   * Indicates the type of function performed at the location.
   */
  type?: CodeableConcept[];
}
/**
 * Code Values for the Location.mode field
 */
export enum LocationModeCodes {
  INSTANCE = 'instance',
  KIND = 'kind',
}
/**
 * Code Values for the Location.status field
 */
export enum LocationStatusCodes {
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  INACTIVE = 'inactive',
}
/**
 * A population criteria for the measure.
 */
export interface MeasureGroupPopulation extends BackboneElement {
  /**
   * The type of population criteria.
   */
  code?: CodeableConcept;
  /**
   * In the case of a continuous-variable or ratio measure, this may be the name of a function that calculates the value of the individual observation for each patient or event in the population. For these types of measures, individual observations are reported as observation resources included in the evaluatedResources bundle for each patient. See the MeasureReport resource or the Quality Reporting topic for more information.
   */
  criteria: Expression;
  /**
   * The human readable description of this population criteria.
   */
  description?: string;
  _description?: Element;
}
/**
 * Stratifiers are defined either as a single criteria, or as a set of component criteria.
 */
export interface MeasureGroupStratifierComponent extends BackboneElement {
  /**
   * Indicates a meaning for the stratifier component. This can be as simple as a unique identifier, or it can establish meaning in a broader context by drawing from a terminology, allowing stratifiers to be correlated across measures.
   */
  code?: CodeableConcept;
  /**
   * An expression that specifies the criteria for this component of the stratifier. This is typically the name of an expression defined within a referenced library, but it may also be a path to a stratifier element.
   */
  criteria: Expression;
  /**
   * The human readable description of this stratifier criteria component.
   */
  description?: string;
  _description?: Element;
}
/**
 * The stratifier criteria for the measure report, specified as either the name of a valid CQL expression defined within a referenced library or a valid FHIR Resource Path.
 */
export interface MeasureGroupStratifier extends BackboneElement {
  /**
   * Indicates a meaning for the stratifier. This can be as simple as a unique identifier, or it can establish meaning in a broader context by drawing from a terminology, allowing stratifiers to be correlated across measures.
   */
  code?: CodeableConcept;
  /**
   * Stratifiers are defined either as a single criteria, or as a set of component criteria.
   */
  component?: MeasureGroupStratifierComponent[];
  /**
   * An expression that specifies the criteria for the stratifier. This is typically the name of an expression defined within a referenced library, but it may also be a path to a stratifier element.
   */
  criteria?: Expression;
  /**
   * The human readable description of this stratifier criteria.
   */
  description?: string;
  _description?: Element;
}
/**
 * A group of population criteria for the measure.
 */
export interface MeasureGroup extends BackboneElement {
  /**
   * Indicates a meaning for the group. This can be as simple as a unique identifier, or it can establish meaning in a broader context by drawing from a terminology, allowing groups to be correlated across measures.
   */
  code?: CodeableConcept;
  /**
   * The human readable description of this population group.
   */
  description?: string;
  _description?: Element;
  /**
   * A population criteria for the measure.
   */
  population?: MeasureGroupPopulation[];
  /**
   * The stratifier criteria for the measure report, specified as either the name of a valid CQL expression defined within a referenced library or a valid FHIR Resource Path.
   */
  stratifier?: MeasureGroupStratifier[];
}
/**
 * Note that supplemental data are reported as observations for each patient and included in the evaluatedResources bundle. See the MeasureReport resource or the Quality Reporting topic for more information.
 */
export interface MeasureSupplementalData extends BackboneElement {
  /**
   * Indicates a meaning for the supplemental data. This can be as simple as a unique identifier, or it can establish meaning in a broader context by drawing from a terminology, allowing supplemental data to be correlated across measures.
   */
  code?: CodeableConcept;
  /**
   * The criteria for the supplemental data. This is typically the name of a valid expression defined within a referenced library, but it may also be a path to a specific data element. The criteria defines the data to be returned for this element.
   */
  criteria: Expression;
  /**
   * The human readable description of this supplemental data.
   */
  description?: string;
  _description?: Element;
  /**
   * An indicator of the intended usage for the supplemental data element. Supplemental data indicates the data is additional information requested to augment the measure information. Risk adjustment factor indicates the data is additional information used to calculate risk adjustment factors when applying a risk model to the measure calculation.
   */
  usage?: CodeableConcept[];
}
/**
 * The Measure resource provides the definition of a quality measure.
 */
export interface Measure extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Measure';
  /**
   * The 'date' element may be more recent than the approval date because of minor changes or editorial corrections.
   */
  approvalDate?: string;
  _approvalDate?: Element;
  /**
   * An individiual or organization primarily involved in the creation and maintenance of the content.
   */
  author?: ContactDetail[];
  /**
   * Provides a summary of relevant clinical guidelines or other clinical recommendations supporting the measure.
   */
  clinicalRecommendationStatement?: string;
  _clinicalRecommendationStatement?: Element;
  /**
   * If this is a composite measure, the scoring method used to combine the component measures to determine the composite score.
   */
  compositeScoring?: CodeableConcept;
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the measure and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the measure.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the measure. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * Provides a description of an individual term used within the measure.
   */
  definition?: string[];
  _definition?: Element;
  /**
   * This description can be used to capture details such as why the measure was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the measure as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the measure is presumed to be the predominant language in the place the measure was created).
   */
  description?: string;
  _description?: Element;
  /**
   * Notices and disclaimers regarding the use of the measure or related to intellectual property (such as code systems) referenced by the measure.
   */
  disclaimer?: string;
  _disclaimer?: Element;
  /**
   * An individual or organization primarily responsible for internal coherence of the content.
   */
  editor?: ContactDetail[];
  /**
   * The effective period for a measure  determines when the content is applicable for usage and is independent of publication and review dates. For example, a measure intended to be used for the year 2016 might be published in 2015.
   */
  effectivePeriod?: Period;
  /**
   * An individual or organization responsible for officially endorsing the content for use in some setting.
   */
  endorser?: ContactDetail[];
  /**
   * Allows filtering of measures that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * A group of population criteria for the measure.
   */
  group?: MeasureGroup[];
  /**
   * Additional guidance for the measure including how it can be used in a clinical context, and the intent of the measure.
   */
  guidance?: string;
  _guidance?: Element;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this measure outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * Information on whether an increase or decrease in score is the preferred result (e.g., a higher score indicates better quality OR a lower score indicates better quality OR quality is within a range).
   */
  improvementNotation?: CodeableConcept;
  /**
   * It may be possible for the measure to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * If specified, this date follows the original approval date.
   */
  lastReviewDate?: string;
  _lastReviewDate?: Element;
  /**
   * A reference to a Library resource containing the formal logic used by the measure.
   */
  library?: string[];
  _library?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the measure is the organization or individual primarily responsible for the maintenance and upkeep of the measure. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the measure. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the measure. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this measure.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * The measure rate for an organization or clinician is based upon the entities’ aggregate data and summarizes the performance of the entity over a given time period (e.g., monthly, quarterly, yearly). The aggregated data are derived from the results of a specific measure algorithm and, if appropriate, the application of specific risk adjustment models.  Can also be used to describe how to risk adjust the data based on supplemental data elements described in the eMeasure (e.g., pneumonia hospital measures antibiotic selection in the ICU versus non-ICU and then the roll-up of the two). This could be applied to aggregated cohort measure definitions (e.g., CDC's aggregate reporting for TB at the state level).
   */
  rateAggregation?: string;
  _rateAggregation?: Element;
  /**
   * Provides a succinct statement of the need for the measure. Usually includes statements pertaining to importance criterion: impact, gap in care, and evidence.
   */
  rationale?: string;
  _rationale?: Element;
  /**
   * Each related artifact is either an attachment, or a reference to another resource, but not both.
   */
  relatedArtifact?: RelatedArtifact[];
  /**
   * An individual or organization primarily responsible for review of some aspect of the content.
   */
  reviewer?: ContactDetail[];
  /**
   * Describes the method of adjusting for clinical severity and conditions present at the start of care that can influence patient outcomes for making valid comparisons of outcome measures across providers. Indicates whether a measure is subject to the statistical process for reducing, removing, or clarifying the influences of confounding factors to allow for more useful comparisons.
   */
  riskAdjustment?: string;
  _riskAdjustment?: Element;
  /**
   * Indicates how the calculation is performed for the measure, including proportion, ratio, continuous-variable, and cohort. The value set is extensible, allowing additional measure scoring types to be represented.
   */
  scoring?: CodeableConcept;
  /**
   * Allows filtering of measures that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * The subject of the measure is critical in interpreting the criteria definitions, as the logic in the measures is evaluated with respect to a particular subject. This corresponds roughly to the notion of a Compartment in that it limits what content is available based on its relationship to the subject. In CQL, this corresponds to the context declaration.
   */
  subjectCodeableConcept?: CodeableConcept;
  /**
   * The subject of the measure is critical in interpreting the criteria definitions, as the logic in the measures is evaluated with respect to a particular subject. This corresponds roughly to the notion of a Compartment in that it limits what content is available based on its relationship to the subject. In CQL, this corresponds to the context declaration.
   */
  subjectReference?: Reference;
  /**
   * An explanatory or alternate title for the measure giving additional information about its content.
   */
  subtitle?: string;
  _subtitle?: Element;
  /**
   * Note that supplemental data are reported as observations for each patient and included in the evaluatedResources bundle. See the MeasureReport resource or the Quality Reporting topic for more information.
   */
  supplementalData?: MeasureSupplementalData[];
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Descriptive topics related to the content of the measure. Topics provide a high-level categorization grouping types of measures that can be useful for filtering and searching.
   */
  topic?: CodeableConcept[];
  /**
   * Indicates whether the measure is used to examine a process, an outcome over time, a patient-reported outcome, or a structure measure such as utilization.
   */
  type?: CodeableConcept[];
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * A detailed description, from a clinical perspective, of how the measure is used.
   */
  usage?: string;
  _usage?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different measure instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the measure with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the Measure.status field
 */
export enum MeasureStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * The populations that make up the population group, one for each type of population appropriate for the measure.
 */
export interface MeasureReportGroupPopulation extends BackboneElement {
  /**
   * The type of the population.
   */
  code?: CodeableConcept;
  /**
   * The number of members of the population.
   */
  count?: number;
  /**
   * This element refers to a List of subject level MeasureReport resources, one for each subject in this population.
   */
  subjectResults?: Reference;
}
/**
 * A stratifier component value.
 */
export interface MeasureReportGroupStratifierStratumComponent extends BackboneElement {
  /**
   * The code for the stratum component value.
   */
  code: CodeableConcept;
  /**
   * The stratum component value.
   */
  value: CodeableConcept;
}
/**
 * The populations that make up the stratum, one for each type of population appropriate to the measure.
 */
export interface MeasureReportGroupStratifierStratumPopulation extends BackboneElement {
  /**
   * The type of the population.
   */
  code?: CodeableConcept;
  /**
   * The number of members of the population in this stratum.
   */
  count?: number;
  /**
   * This element refers to a List of subject level MeasureReport resources, one for each subject in this population in this stratum.
   */
  subjectResults?: Reference;
}
/**
 * This element contains the results for a single stratum within the stratifier. For example, when stratifying on administrative gender, there will be four strata, one for each possible gender value.
 */
export interface MeasureReportGroupStratifierStratum extends BackboneElement {
  /**
   * A stratifier component value.
   */
  component?: MeasureReportGroupStratifierStratumComponent[];
  /**
   * The measure score for this stratum, calculated as appropriate for the measure type and scoring method, and based on only the members of this stratum.
   */
  measureScore?: Quantity;
  /**
   * The populations that make up the stratum, one for each type of population appropriate to the measure.
   */
  population?: MeasureReportGroupStratifierStratumPopulation[];
  /**
   * The value for this stratum, expressed as a CodeableConcept. When defining stratifiers on complex values, the value must be rendered such that the value for each stratum within the stratifier is unique.
   */
  value?: CodeableConcept;
}
/**
 * When a measure includes multiple stratifiers, there will be a stratifier group for each stratifier defined by the measure.
 */
export interface MeasureReportGroupStratifier extends BackboneElement {
  /**
   * The meaning of this stratifier, as defined in the measure definition.
   */
  code?: CodeableConcept[];
  /**
   * This element contains the results for a single stratum within the stratifier. For example, when stratifying on administrative gender, there will be four strata, one for each possible gender value.
   */
  stratum?: MeasureReportGroupStratifierStratum[];
}
/**
 * The results of the calculation, one for each population group in the measure.
 */
export interface MeasureReportGroup extends BackboneElement {
  /**
   * The meaning of the population group as defined in the measure definition.
   */
  code?: CodeableConcept;
  /**
   * The measure score for this population group, calculated as appropriate for the measure type and scoring method, and based on the contents of the populations defined in the group.
   */
  measureScore?: Quantity;
  /**
   * The populations that make up the population group, one for each type of population appropriate for the measure.
   */
  population?: MeasureReportGroupPopulation[];
  /**
   * When a measure includes multiple stratifiers, there will be a stratifier group for each stratifier defined by the measure.
   */
  stratifier?: MeasureReportGroupStratifier[];
}
/**
 * The MeasureReport resource contains the results of the calculation of a measure; and optionally a reference to the resources involved in that calculation.
 */
export interface MeasureReport extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MeasureReport';
  /**
   * The date this measure report was generated.
   */
  date?: string;
  _date?: Element;
  /**
   * A reference to a Bundle containing the Resources that were used in the calculation of this measure.
   */
  evaluatedResource?: Reference[];
  /**
   * The results of the calculation, one for each population group in the measure.
   */
  group?: MeasureReportGroup[];
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II data type - e.g. to identify this {{title}} outside of FHIR, where the logical URL is not possible to use.
   */
  identifier?: Identifier[];
  /**
   * This element is typically defined by the measure, but reproduced here to ensure the measure score can be interpreted. The element is labeled as a modifier because it changes the interpretation of the reported measure score.
   */
  improvementNotation?: CodeableConcept;
  /**
   * A reference to the Measure that was calculated to produce this report.
   */
  measure: string;
  _measure?: Element;
  /**
   * The reporting period for which the report was calculated.
   */
  period: Period;
  /**
   * The individual, location, or organization that is reporting the data.
   */
  reporter?: Reference;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * Optional subject identifying the individual or individuals the report is for.
   */
  subject?: Reference;
  /**
   * Data-collection reports are used only to communicate data-of-interest for a measure. They do not necessarily include all the data for a particular subject or population, but they may.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the MeasureReport.status field
 */
export enum MeasureReportStatusCodes {
  COMPLETE = 'complete',
  PENDING = 'pending',
  ERROR = 'error',
}
/**
 * Code Values for the MeasureReport.type field
 */
export enum MeasureReportTypeCodes {
  INDIVIDUAL = 'individual',
  SUBJECT_LIST = 'subject-list',
  SUMMARY = 'summary',
  DATA_COLLECTION = 'data-collection',
}
/**
 * A photo, video, or audio recording acquired or used in healthcare. The actual content may be inline or provided by direct reference.
 */
export interface Media extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Media';
  /**
   * A procedure that is fulfilled in whole or in part by the creation of this media.
   */
  basedOn?: Reference[];
  /**
   * Only used if not implicit in code found in Observation.code.  In many systems, this may be represented as a related observation instead of an inline component.
   * If the use case requires BodySite to be handled as a separate resource (e.g. to identify and track separately) then use the standard extension[ bodySite](extension-bodysite.html).
   */
  bodySite?: CodeableConcept;
  /**
   * Recommended content types: image/jpeg, image/png, image/tiff, video/mpeg, audio/mp4, application/dicom. Application/dicom can contain the transfer syntax as a parameter.  For media that covers a period of time (video/sound), the content.creationTime is the end time. Creation time is used for tracking, organizing versions and searching.
   */
  content: Attachment;
  /**
   * The date and time(s) at which the media was collected.
   */
  createdDateTime?: string;
  _createdDateTime?: Element;
  /**
   * The date and time(s) at which the media was collected.
   */
  createdPeriod?: Period;
  /**
   * An extension should be used if further typing of the device is needed.  Secondary devices used to support collecting a media can be represented using either extension or through the Observation.related element.
   */
  device?: Reference;
  /**
   * The name of the device / manufacturer of the device  that was used to make the recording.
   */
  deviceName?: string;
  _deviceName?: Element;
  /**
   * The duration might differ from occurrencePeriod if recording was paused.
   */
  duration?: number;
  /**
   * This will typically be the encounter the media occurred within.
   */
  encounter?: Reference;
  /**
   * if the number of frames is not supplied, the value may be unknown. Applications should not assume that there is only one frame unless it is explicitly stated.
   */
  frames?: number;
  /**
   * Height of the image in pixels (photo/video).
   */
  height?: number;
  /**
   * The identifier label and use can be used to determine what kind of identifier it is.
   */
  identifier?: Identifier[];
  /**
   * It may be the same as the [`lastUpdated` ](resource-definitions.html#Meta.lastUpdated) time of the resource itself.  For Observations that do require review and verification for certain updates, it might not be the same as the `lastUpdated` time of the resource itself due to a non-clinically significant update that does not require the new version to be reviewed and verified again.
   */
  issued?: string;
  _issued?: Element;
  /**
   * Details of the type of the media - usually, how it was acquired (what type of device). If images sourced from a DICOM system, are wrapped in a Media resource, then this is the modality.
   */
  modality?: CodeableConcept;
  /**
   * Not to be used for observations, conclusions, etc. Instead use an [Observation](observation.html) based on the Media/ImagingStudy resource.
   */
  note?: Annotation[];
  /**
   * The person who administered the collection of the image.
   */
  operator?: Reference;
  /**
   * Not to be used to link an event to an Encounter - use Media.encounter for that.
   * [The allowed reference resources may be adjusted as appropriate for the event resource].
   */
  partOf?: Reference[];
  /**
   * Textual reasons can be captured using reasonCode.text.
   */
  reasonCode?: CodeableConcept[];
  /**
   * A nominal state-transition diagram can be found in the [[event.html#statemachine | Event pattern]] documentation
   * Unknown does not represent "other" - one of the defined statuses must apply.  Unknown is used when the authoring system is not sure what the current status is.
   */
  status: string;
  _status?: Element;
  /**
   * Who/What this Media is a record of.
   */
  subject?: Reference;
  /**
   * A code that classifies whether the media is an image, video or audio recording or some other media category.
   */
  type?: CodeableConcept;
  /**
   * The name of the imaging view e.g. Lateral or Antero-posterior (AP).
   */
  view?: CodeableConcept;
  /**
   * Width of the image in pixels (photo/video).
   */
  width?: number;
}
/**
 * Code Values for the Media.status field
 */
export enum MediaStatusCodes {
  PREPARATION = 'preparation',
  IN_PROGRESS = 'in-progress',
  NOT_DONE = 'not-done',
  ON_HOLD = 'on-hold',
  STOPPED = 'stopped',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  UNKNOWN = 'unknown',
}
/**
 * The ingredients need not be a complete list.  If an ingredient is not specified, this does not indicate whether an ingredient is present or absent.  If an ingredient is specified it does not mean that all ingredients are specified.  It is possible to specify both inactive and active ingredients.
 */
export interface MedicationIngredient extends BackboneElement {
  /**
   * Indication of whether this ingredient affects the therapeutic action of the drug.
   */
  isActive?: boolean;
  _isActive?: Element;
  /**
   * The actual ingredient - either a substance (simple ingredient) or another medication of a medication.
   */
  itemCodeableConcept: CodeableConcept;
  /**
   * The actual ingredient - either a substance (simple ingredient) or another medication of a medication.
   */
  itemReference: Reference;
  /**
   * Specifies how many (or how much) of the items there are in this Medication.  For example, 250 mg per tablet.  This is expressed as a ratio where the numerator is 250mg and the denominator is 1 tablet.
   */
  strength?: Ratio;
}
/**
 * Information that only applies to packages (not products).
 */
export interface MedicationBatch extends BackboneElement {
  /**
   * When this specific batch of product will expire.
   */
  expirationDate?: string;
  _expirationDate?: Element;
  /**
   * The assigned lot number of a batch of the specified product.
   */
  lotNumber?: string;
  _lotNumber?: Element;
}
/**
 * This resource is primarily used for the identification and definition of a medication for the purposes of prescribing, dispensing, and administering a medication as well as for making statements about medication use.
 */
export interface Medication extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Medication';
  /**
   * Specific amount of the drug in the packaged product.  For example, when specifying a product that has the same strength (For example, Insulin glargine 100 unit per mL solution for injection), this attribute provides additional clarification of the package amount (For example, 3 mL, 10mL, etc.).
   */
  amount?: Ratio;
  /**
   * Information that only applies to packages (not products).
   */
  batch?: MedicationBatch;
  /**
   * Depending on the context of use, the code that was actually selected by the user (prescriber, dispenser, etc.) will have the coding.userSelected set to true.  As described in the coding datatype: "A coding may be marked as a "userSelected" if a user selected the particular coded value in a user interface (e.g. the user selects an item in a pick-list). If a user selected coding exists, it is the preferred choice for performing translations etc. Other codes can only be literal translations to alternative code systems, or codes at a lower level of granularity (e.g. a generic code for a vendor-specific primary one).
   */
  code?: CodeableConcept;
  /**
   * When Medication is referenced from MedicationRequest, this is the ordered form.  When Medication is referenced within MedicationDispense, this is the dispensed form.  When Medication is referenced within MedicationAdministration, this is administered form.
   */
  form?: CodeableConcept;
  /**
   * The serial number could be included as an identifier.
   */
  identifier?: Identifier[];
  /**
   * The ingredients need not be a complete list.  If an ingredient is not specified, this does not indicate whether an ingredient is present or absent.  If an ingredient is specified it does not mean that all ingredients are specified.  It is possible to specify both inactive and active ingredients.
   */
  ingredient?: MedicationIngredient[];
  /**
   * Describes the details of the manufacturer of the medication product.  This is not intended to represent the distributor of a medication product.
   */
  manufacturer?: Reference;
  /**
   * This status is intended to identify if the medication in a local system is in active use within a drug database or inventory.  For example, a pharmacy system may create a new drug file record for a compounded product "ABC Hospital Special Cream" with an active status.  At some point in the future, it may be determined that the drug record was created with an error and the status is changed to "entered in error".   This status is not intended to specify if a medication is part of a particular formulary.  It is possible that the drug record may be referenced by multiple formularies or catalogues and each of those entries would have a separate status.
   */
  status?: string;
  _status?: Element;
}
/**
 * Code Values for the Medication.status field
 */
export enum MedicationStatusCodes {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Indicates who or what performed the medication administration and how they were involved.
 */
export interface MedicationAdministrationPerformer extends BackboneElement {
  /**
   * Indicates who or what performed the medication administration.
   */
  actor: Reference;
  /**
   * Distinguishes the type of involvement of the performer in the medication administration.
   */
  function?: CodeableConcept;
}
/**
 * Describes the medication dosage information details e.g. dose, rate, site, route, etc.
 */
export interface MedicationAdministrationDosage extends BackboneElement {
  /**
   * If the administration is not instantaneous (rate is present), this can be specified to convey the total amount administered over period of time of a single administration.
   */
  dose?: Quantity;
  /**
   * One of the reasons this attribute is not used often, is that the method is often pre-coordinated with the route and/or form of administration.  This means the codes used in route or form may pre-coordinate the method in the route code or the form code.  The implementation decision about what coding system to use for route or form code will determine how frequently the method code will be populated e.g. if route or form code pre-coordinate method code, then this attribute will not be populated often; if there is no pre-coordination then method code may  be used frequently.
   */
  method?: CodeableConcept;
  /**
   * If the rate changes over time, and you want to capture this in MedicationAdministration, then each change should be captured as a distinct MedicationAdministration, with a specific MedicationAdministration.dosage.rate, and the date time when the rate change occurred. Typically, the MedicationAdministration.dosage.rate element is not used to convey an average rate.
   */
  rateRatio?: Ratio;
  /**
   * If the rate changes over time, and you want to capture this in MedicationAdministration, then each change should be captured as a distinct MedicationAdministration, with a specific MedicationAdministration.dosage.rate, and the date time when the rate change occurred. Typically, the MedicationAdministration.dosage.rate element is not used to convey an average rate.
   */
  rateQuantity?: Quantity;
  /**
   * A code specifying the route or physiological path of administration of a therapeutic agent into or onto the patient.  For example, topical, intravenous, etc.
   */
  route?: CodeableConcept;
  /**
   * If the use case requires attributes from the BodySite resource (e.g. to identify and track separately) then use the standard extension [bodySite](extension-bodysite.html).  May be a summary code, or a reference to a very precise definition of the location, or both.
   */
  site?: CodeableConcept;
  /**
   * Free text dosage can be used for cases where the dosage administered is too complex to code. When coded dosage is present, the free text dosage may still be present for display to humans.
   * The dosage instructions should reflect the dosage of the medication that was administered.
   */
  text?: string;
  _text?: Element;
}
/**
 * Describes the event of a patient consuming or otherwise being administered a medication.  This may be as simple as swallowing a tablet or it may be a long running infusion.  Related resources tie this event to the authorizing prescription, and the specific encounter between patient and health care practitioner.
 */
export interface MedicationAdministration extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicationAdministration';
  /**
   * Indicates where the medication is expected to be consumed or administered.
   */
  category?: CodeableConcept;
  /**
   * The visit, admission, or other contact between patient and health care provider during which the medication administration was performed.
   */
  context?: Reference;
  /**
   * The device used in administering the medication to the patient.  For example, a particular infusion pump.
   */
  device?: Reference[];
  /**
   * Describes the medication dosage information details e.g. dose, rate, site, route, etc.
   */
  dosage?: MedicationAdministrationDosage;
  /**
   * A specific date/time or interval of time during which the administration took place (or did not take place, when the 'notGiven' attribute is true). For many administrations, such as swallowing a tablet the use of dateTime is more appropriate.
   */
  effectiveDateTime: string;
  _effectiveDateTime?: Element;
  /**
   * A specific date/time or interval of time during which the administration took place (or did not take place, when the 'notGiven' attribute is true). For many administrations, such as swallowing a tablet the use of dateTime is more appropriate.
   */
  effectivePeriod: Period;
  /**
   * This might not include provenances for all versions of the request – only those deemed “relevant” or important. This SHALL NOT include the Provenance associated with this current version of the resource. (If that provenance is deemed to be a “relevant” change, it will need to be added as part of a later update. Until then, it can be queried directly as the Provenance that points to this version using _revinclude All Provenances should have some historical version of this Request as their subject.
   */
  eventHistory?: Reference[];
  /**
   * This is a business identifier, not a resource identifier.
   */
  identifier?: Identifier[];
  /**
   * A protocol, guideline, orderset, or other definition that was adhered to in whole or in part by this event.
   */
  instantiates?: string[];
  _instantiates?: Element;
  /**
   * If only a code is specified, then it needs to be a code for a specific product. If more information is required, then the use of the medication resource is recommended.  For example, if you require form or lot number, then you must reference the Medication resource.
   */
  medicationCodeableConcept: CodeableConcept;
  /**
   * If only a code is specified, then it needs to be a code for a specific product. If more information is required, then the use of the medication resource is recommended.  For example, if you require form or lot number, then you must reference the Medication resource.
   */
  medicationReference: Reference;
  /**
   * Extra information about the medication administration that is not conveyed by the other attributes.
   */
  note?: Annotation[];
  /**
   * A larger event of which this particular event is a component or step.
   */
  partOf?: Reference[];
  /**
   * Indicates who or what performed the medication administration and how they were involved.
   */
  performer?: MedicationAdministrationPerformer[];
  /**
   * A code indicating why the medication was given.
   */
  reasonCode?: CodeableConcept[];
  /**
   * This is a reference to a condition that is the reason for the medication request.  If only a code exists, use reasonCode.
   */
  reasonReference?: Reference[];
  /**
   * This is a reference to the MedicationRequest  where the intent is either order or instance-order.  It should not reference MedicationRequests where the intent is any other value.
   */
  request?: Reference;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * A code indicating why the administration was not performed.
   */
  statusReason?: CodeableConcept[];
  /**
   * The person or animal or group receiving the medication.
   */
  subject: Reference;
  /**
   * Additional information (for example, patient height and weight) that supports the administration of the medication.
   */
  supportingInformation?: Reference[];
}
/**
 * Code Values for the MedicationAdministration.status field
 */
export enum MedicationAdministrationStatusCodes {
  IN_PROGRESS = 'in-progress',
  NOT_DONE = 'not-done',
  ON_HOLD = 'on-hold',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  STOPPED = 'stopped',
  UNKNOWN = 'unknown',
}
/**
 * Indicates who or what performed the event.
 */
export interface MedicationDispensePerformer extends BackboneElement {
  /**
   * The device, practitioner, etc. who performed the action.  It should be assumed that the actor is the dispenser of the medication.
   */
  actor: Reference;
  /**
   * Distinguishes the type of performer in the dispense.  For example, date enterer, packager, final checker.
   */
  function?: CodeableConcept;
}
/**
 * Indicates whether or not substitution was made as part of the dispense.  In some cases, substitution will be expected but does not happen, in other cases substitution is not expected but does happen.  This block explains what substitution did or did not happen and why.  If nothing is specified, substitution was not done.
 */
export interface MedicationDispenseSubstitution extends BackboneElement {
  /**
   * Indicates the reason for the substitution (or lack of substitution) from what was prescribed.
   */
  reason?: CodeableConcept[];
  /**
   * The person or organization that has primary responsibility for the substitution.
   */
  responsibleParty?: Reference[];
  /**
   * A code signifying whether a different drug was dispensed from what was prescribed.
   */
  type?: CodeableConcept;
  /**
   * True if the dispenser dispensed a different drug or product from what was prescribed.
   */
  wasSubstituted: boolean;
  _wasSubstituted?: Element;
}
/**
 * Indicates that a medication product is to be or has been dispensed for a named person/patient.  This includes a description of the medication product (supply) provided and the instructions for administering the medication.  The medication dispense is the result of a pharmacy system responding to a medication order.
 */
export interface MedicationDispense extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicationDispense';
  /**
   * Maps to basedOn in Event logical model.
   */
  authorizingPrescription?: Reference[];
  /**
   * The category can be used to include where the medication is expected to be consumed or other types of dispenses.  Invariants can be used to bind to different value sets when profiling to bind.
   */
  category?: CodeableConcept;
  /**
   * The encounter or episode of care that establishes the context for this event.
   */
  context?: Reference;
  /**
   * The amount of medication expressed as a timing amount.
   */
  daysSupply?: Quantity;
  /**
   * Identification of the facility/location where the medication was shipped to, as part of the dispense event.
   */
  destination?: Reference;
  /**
   * This element can include a detected issue that has been identified either by a decision support system or by a clinician and may include information on the steps that were taken to address the issue.
   */
  detectedIssue?: Reference[];
  /**
   * When the dose or rate is intended to change over the entire administration period (e.g. Tapering dose prescriptions), multiple instances of dosage instructions will need to be supplied to convey the different doses/rates.
   * The pharmacist reviews the medication order prior to dispense and updates the dosageInstruction based on the actual product being dispensed.
   */
  dosageInstruction?: Dosage[];
  /**
   * This might not include provenances for all versions of the request – only those deemed “relevant” or important. This SHALL NOT include the Provenance associated with this current version of the resource. (If that provenance is deemed to be a “relevant” change, it will need to be added as part of a later update. Until then, it can be queried directly as the Provenance that points to this version using _revinclude All Provenances should have some historical version of this Request as their subject.).
   */
  eventHistory?: Reference[];
  /**
   * This is a business identifier, not a resource identifier.
   */
  identifier?: Identifier[];
  /**
   * The principal physical location where the dispense was performed.
   */
  location?: Reference;
  /**
   * If only a code is specified, then it needs to be a code for a specific product. If more information is required, then the use of the medication resource is recommended.  For example, if you require form or lot number, then you must reference the Medication resource.
   */
  medicationCodeableConcept: CodeableConcept;
  /**
   * If only a code is specified, then it needs to be a code for a specific product. If more information is required, then the use of the medication resource is recommended.  For example, if you require form or lot number, then you must reference the Medication resource.
   */
  medicationReference: Reference;
  /**
   * Extra information about the dispense that could not be conveyed in the other attributes.
   */
  note?: Annotation[];
  /**
   * The procedure that trigger the dispense.
   */
  partOf?: Reference[];
  /**
   * Indicates who or what performed the event.
   */
  performer?: MedicationDispensePerformer[];
  /**
   * The amount of medication that has been dispensed. Includes unit of measure.
   */
  quantity?: Quantity;
  /**
   * Identifies the person who picked up the medication.  This will usually be a patient or their caregiver, but some cases exist where it can be a healthcare professional.
   */
  receiver?: Reference[];
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * Indicates the reason why a dispense was not performed.
   */
  statusReasonCodeableConcept?: CodeableConcept;
  /**
   * Indicates the reason why a dispense was not performed.
   */
  statusReasonReference?: Reference;
  /**
   * SubstanceAdministration->subject->Patient.
   */
  subject?: Reference;
  /**
   * Indicates whether or not substitution was made as part of the dispense.  In some cases, substitution will be expected but does not happen, in other cases substitution is not expected but does happen.  This block explains what substitution did or did not happen and why.  If nothing is specified, substitution was not done.
   */
  substitution?: MedicationDispenseSubstitution;
  /**
   * Additional information that supports the medication being dispensed.
   */
  supportingInformation?: Reference[];
  /**
   * Indicates the type of dispensing event that is performed. For example, Trial Fill, Completion of Trial, Partial Fill, Emergency Fill, Samples, etc.
   */
  type?: CodeableConcept;
  /**
   * The time the dispensed product was provided to the patient or their representative.
   */
  whenHandedOver?: string;
  _whenHandedOver?: Element;
  /**
   * The time when the dispensed product was packaged and reviewed.
   */
  whenPrepared?: string;
  _whenPrepared?: Element;
}
/**
 * Code Values for the MedicationDispense.status field
 */
export enum MedicationDispenseStatusCodes {
  PREPARATION = 'preparation',
  IN_PROGRESS = 'in-progress',
  CANCELLED = 'cancelled',
  ON_HOLD = 'on-hold',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  STOPPED = 'stopped',
  DECLINED = 'declined',
  UNKNOWN = 'unknown',
}
/**
 * Associated or related knowledge about a medication.
 */
export interface MedicationKnowledgeRelatedMedicationKnowledge extends BackboneElement {
  /**
   * Associated documentation about the associated medication knowledge.
   */
  reference: Reference[];
  /**
   * The category of the associated medication knowledge reference.
   */
  type: CodeableConcept;
}
/**
 * Associated documentation about the medication.
 */
export interface MedicationKnowledgeMonograph extends BackboneElement {
  /**
   * Associated documentation about the medication.
   */
  source?: Reference;
  /**
   * The category of documentation about the medication. (e.g. professional monograph, patient education monograph).
   */
  type?: CodeableConcept;
}
/**
 * Identifies a particular constituent of interest in the product.
 */
export interface MedicationKnowledgeIngredient extends BackboneElement {
  /**
   * Indication of whether this ingredient affects the therapeutic action of the drug.
   */
  isActive?: boolean;
  _isActive?: Element;
  /**
   * The actual ingredient - either a substance (simple ingredient) or another medication.
   */
  itemCodeableConcept: CodeableConcept;
  /**
   * The actual ingredient - either a substance (simple ingredient) or another medication.
   */
  itemReference: Reference;
  /**
   * Specifies how many (or how much) of the items there are in this Medication.  For example, 250 mg per tablet.  This is expressed as a ratio where the numerator is 250mg and the denominator is 1 tablet.
   */
  strength?: Ratio;
}
/**
 * The price of the medication.
 */
export interface MedicationKnowledgeCost extends BackboneElement {
  /**
   * The price of the medication.
   */
  cost: Money;
  /**
   * The source or owner that assigns the price to the medication.
   */
  source?: string;
  _source?: Element;
  /**
   * The category of the cost information.  For example, manufacturers' cost, patient cost, claim reimbursement cost, actual acquisition cost.
   */
  type: CodeableConcept;
}
/**
 * The program under which the medication is reviewed.
 */
export interface MedicationKnowledgeMonitoringProgram extends BackboneElement {
  /**
   * Name of the reviewing program.
   */
  name?: string;
  _name?: Element;
  /**
   * Type of program under which the medication is monitored.
   */
  type?: CodeableConcept;
}
/**
 * Dosage for the medication for the specific guidelines.
 */
export interface MedicationKnowledgeAdministrationGuidelinesDosage extends BackboneElement {
  /**
   * Dosage for the medication for the specific guidelines.
   */
  dosage: Dosage[];
  /**
   * The type of dosage (for example, prophylaxis, maintenance, therapeutic, etc.).
   */
  type: CodeableConcept;
}
/**
 * Characteristics of the patient that are relevant to the administration guidelines (for example, height, weight, gender, etc.).
 */
export interface MedicationKnowledgeAdministrationGuidelinesPatientCharacteristics extends BackboneElement {
  /**
   * Specific characteristic that is relevant to the administration guideline (e.g. height, weight, gender).
   */
  characteristicCodeableConcept: CodeableConcept;
  /**
   * Specific characteristic that is relevant to the administration guideline (e.g. height, weight, gender).
   */
  characteristicQuantity: Quantity;
  /**
   * The specific characteristic (e.g. height, weight, gender, etc.).
   */
  value?: string[];
  _value?: Element;
}
/**
 * Guidelines for the administration of the medication.
 */
export interface MedicationKnowledgeAdministrationGuidelines extends BackboneElement {
  /**
   * Dosage for the medication for the specific guidelines.
   */
  dosage?: MedicationKnowledgeAdministrationGuidelinesDosage[];
  /**
   * Indication for use that apply to the specific administration guidelines.
   */
  indicationCodeableConcept?: CodeableConcept;
  /**
   * Indication for use that apply to the specific administration guidelines.
   */
  indicationReference?: Reference;
  /**
   * Characteristics of the patient that are relevant to the administration guidelines (for example, height, weight, gender, etc.).
   */
  patientCharacteristics?: MedicationKnowledgeAdministrationGuidelinesPatientCharacteristics[];
}
/**
 * Categorization of the medication within a formulary or classification system.
 */
export interface MedicationKnowledgeMedicineClassification extends BackboneElement {
  /**
   * Specific category assigned to the medication (e.g. anti-infective, anti-hypertensive, antibiotic, etc.).
   */
  classification?: CodeableConcept[];
  /**
   * The type of category for the medication (for example, therapeutic classification, therapeutic sub-classification).
   */
  type: CodeableConcept;
}
/**
 * Information that only applies to packages (not products).
 */
export interface MedicationKnowledgePackaging extends BackboneElement {
  /**
   * The number of product units the package would contain if fully loaded.
   */
  quantity?: Quantity;
  /**
   * A code that defines the specific type of packaging that the medication can be found in (e.g. blister sleeve, tube, bottle).
   */
  type?: CodeableConcept;
}
/**
 * Specifies descriptive properties of the medicine, such as color, shape, imprints, etc.
 */
export interface MedicationKnowledgeDrugCharacteristic extends BackboneElement {
  /**
   * A code specifying which characteristic of the medicine is being described (for example, colour, shape, imprint).
   */
  type?: CodeableConcept;
  /**
   * The description should be provided as a CodeableConcept, SimpleQuantity or an image.  The description can be a string only when these others are not available.
   */
  valueCodeableConcept?: CodeableConcept;
  /**
   * The description should be provided as a CodeableConcept, SimpleQuantity or an image.  The description can be a string only when these others are not available.
   */
  valueString?: string;
  _valueString?: Element;
  /**
   * The description should be provided as a CodeableConcept, SimpleQuantity or an image.  The description can be a string only when these others are not available.
   */
  valueQuantity?: Quantity;
  /**
   * The description should be provided as a CodeableConcept, SimpleQuantity or an image.  The description can be a string only when these others are not available.
   */
  valueBase64Binary?: string;
  _valueBase64Binary?: Element;
}
/**
 * Specifies if changes are allowed when dispensing a medication from a regulatory perspective.
 */
export interface MedicationKnowledgeRegulatorySubstitution extends BackboneElement {
  /**
   * Specifies if regulation allows for changes in the medication when dispensing.
   */
  allowed: boolean;
  _allowed?: Element;
  /**
   * Specifies the type of substitution allowed.
   */
  type: CodeableConcept;
}
/**
 * Specifies the schedule of a medication in jurisdiction.
 */
export interface MedicationKnowledgeRegulatorySchedule extends BackboneElement {
  /**
   * Specifies the specific drug schedule.
   */
  schedule: CodeableConcept;
}
/**
 * The maximum number of units of the medication that can be dispensed in a period.
 */
export interface MedicationKnowledgeRegulatoryMaxDispense extends BackboneElement {
  /**
   * The period that applies to the maximum number of units.
   */
  period?: Duration;
  /**
   * The maximum number of units of the medication that can be dispensed.
   */
  quantity: Quantity;
}
/**
 * Regulatory information about a medication.
 */
export interface MedicationKnowledgeRegulatory extends BackboneElement {
  /**
   * The maximum number of units of the medication that can be dispensed in a period.
   */
  maxDispense?: MedicationKnowledgeRegulatoryMaxDispense;
  /**
   * The authority that is specifying the regulations.
   */
  regulatoryAuthority: Reference;
  /**
   * Specifies the schedule of a medication in jurisdiction.
   */
  schedule?: MedicationKnowledgeRegulatorySchedule[];
  /**
   * Specifies if changes are allowed when dispensing a medication from a regulatory perspective.
   */
  substitution?: MedicationKnowledgeRegulatorySubstitution[];
}
/**
 * The time course of drug absorption, distribution, metabolism and excretion of a medication from the body.
 */
export interface MedicationKnowledgeKinetics extends BackboneElement {
  /**
   * The drug concentration measured at certain discrete points in time.
   */
  areaUnderCurve?: Quantity[];
  /**
   * The time required for any specified property (e.g., the concentration of a substance in the body) to decrease by half.
   */
  halfLifePeriod?: Duration;
  /**
   * The median lethal dose of a drug.
   */
  lethalDose50?: Quantity[];
}
/**
 * Information about a medication that is used to support knowledge.
 */
export interface MedicationKnowledge extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicationKnowledge';
  /**
   * Guidelines for the administration of the medication.
   */
  administrationGuidelines?: MedicationKnowledgeAdministrationGuidelines[];
  /**
   * This is the quantity of medication in a package.  To specify the strength of the medication, the Ingredient.strength attribute is used.
   */
  amount?: Quantity;
  /**
   * Associated or related medications.  For example, if the medication is a branded product (e.g. Crestor), this is the Therapeutic Moeity (e.g. Rosuvastatin) or if this is a generic medication (e.g. Rosuvastatin), this would link to a branded product (e.g. Crestor).
   */
  associatedMedication?: Reference[];
  /**
   * Depending on the context of use, the code that was actually selected by the user (prescriber, dispenser, etc.) will have the coding.userSelected set to true.  As described in the coding datatype: "A coding may be marked as a "userSelected" if a user selected the particular coded value in a user interface (e.g. the user selects an item in a pick-list). If a user selected coding exists, it is the preferred choice for performing translations etc. Other codes can only be literal translations to alternative code systems, or codes at a lower level of granularity (e.g. a generic code for a vendor-specific primary one).
   */
  code?: CodeableConcept;
  /**
   * Potential clinical issue with or between medication(s) (for example, drug-drug interaction, drug-disease contraindication, drug-allergy interaction, etc.).
   */
  contraindication?: Reference[];
  /**
   * The price of the medication.
   */
  cost?: MedicationKnowledgeCost[];
  /**
   * When Medication is referenced from MedicationRequest, this is the ordered form.  When Medication is referenced within MedicationDispense, this is the dispensed form.  When Medication is referenced within MedicationAdministration, this is administered form.
   */
  doseForm?: CodeableConcept;
  /**
   * Specifies descriptive properties of the medicine, such as color, shape, imprints, etc.
   */
  drugCharacteristic?: MedicationKnowledgeDrugCharacteristic[];
  /**
   * Identifies a particular constituent of interest in the product.
   */
  ingredient?: MedicationKnowledgeIngredient[];
  /**
   * The intended or approved route of administration.
   */
  intendedRoute?: CodeableConcept[];
  /**
   * The time course of drug absorption, distribution, metabolism and excretion of a medication from the body.
   */
  kinetics?: MedicationKnowledgeKinetics[];
  /**
   * Describes the details of the manufacturer of the medication product.  This is not intended to represent the distributor of a medication product.
   */
  manufacturer?: Reference;
  /**
   * Categorization of the medication within a formulary or classification system.
   */
  medicineClassification?: MedicationKnowledgeMedicineClassification[];
  /**
   * The program under which the medication is reviewed.
   */
  monitoringProgram?: MedicationKnowledgeMonitoringProgram[];
  /**
   * Associated documentation about the medication.
   */
  monograph?: MedicationKnowledgeMonograph[];
  /**
   * Information that only applies to packages (not products).
   */
  packaging?: MedicationKnowledgePackaging;
  /**
   * The instructions for preparing the medication.
   */
  preparationInstruction?: string;
  _preparationInstruction?: Element;
  /**
   * Category of the medication or product (e.g. branded product, therapeutic moeity, generic product, innovator product, etc.).
   */
  productType?: CodeableConcept[];
  /**
   * Regulatory information about a medication.
   */
  regulatory?: MedicationKnowledgeRegulatory[];
  /**
   * Associated or related knowledge about a medication.
   */
  relatedMedicationKnowledge?: MedicationKnowledgeRelatedMedicationKnowledge[];
  /**
   * This status is intended to identify if the medication in a local system is in active use within a drug database or inventory.  For example, a pharmacy system may create a new drug file record for a compounded product "ABC Hospital Special Cream" with an active status.  At some point in the future, it may be determined that the drug record was created with an error and the status is changed to "entered in error".   This status is not intended to specify if a medication is part of a particular formulary.  It is possible that the drug record may be referenced by multiple formularies or catalogues and each of those entries would have a separate status.
   */
  status?: string;
  _status?: Element;
  /**
   * Additional names for a medication, for example, the name(s) given to a medication in different countries.  For example, acetaminophen and paracetamol or salbutamol and albuterol.
   */
  synonym?: string[];
  _synonym?: Element;
}
/**
 * Code Values for the MedicationKnowledge.status field
 */
export enum MedicationKnowledgeStatusCodes {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * If populating this element, either the quantity or the duration must be included.
 */
export interface MedicationRequestDispenseRequestInitialFill extends BackboneElement {
  /**
   * The length of time that the first dispense is expected to last.
   */
  duration?: Duration;
  /**
   * The amount or quantity to provide as part of the first dispense.
   */
  quantity?: Quantity;
}
/**
 * Indicates the specific details for the dispense or medication supply part of a medication request (also known as a Medication Prescription or Medication Order).  Note that this information is not always sent with the order.  There may be in some settings (e.g. hospitals) institutional or system support for completing the dispense details in the pharmacy department.
 */
export interface MedicationRequestDispenseRequest extends BackboneElement {
  /**
   * The minimum period of time that must occur between dispenses of the medication.
   */
  dispenseInterval?: Duration;
  /**
   * In some situations, this attribute may be used instead of quantity to identify the amount supplied by how long it is expected to last, rather than the physical quantity issued, e.g. 90 days supply of medication (based on an ordered dosage). When possible, it is always better to specify quantity, as this tends to be more precise. expectedSupplyDuration will always be an estimate that can be influenced by external factors.
   */
  expectedSupplyDuration?: Duration;
  /**
   * If populating this element, either the quantity or the duration must be included.
   */
  initialFill?: MedicationRequestDispenseRequestInitialFill;
  /**
   * If displaying "number of authorized fills", add 1 to this number.
   */
  numberOfRepeatsAllowed?: number;
  /**
   * Indicates the intended dispensing Organization specified by the prescriber.
   */
  performer?: Reference;
  /**
   * The amount that is to be dispensed for one fill.
   */
  quantity?: Quantity;
  /**
   * It reflects the prescribers' perspective for the validity of the prescription. Dispenses must not be made against the prescription outside of this period. The lower-bound of the Dispensing Window signifies the earliest date that the prescription can be filled for the first time. If an upper-bound is not specified then the Prescription is open-ended or will default to a stale-date based on regulations.
   */
  validityPeriod?: Period;
}
/**
 * Indicates whether or not substitution can or should be part of the dispense. In some cases, substitution must happen, in other cases substitution must not happen. This block explains the prescriber's intent. If nothing is specified substitution may be done.
 */
export interface MedicationRequestSubstitution extends BackboneElement {
  /**
   * This element is labeled as a modifier because whether substitution is allow or not, it cannot be ignored.
   */
  allowedBoolean: boolean;
  _allowedBoolean?: Element;
  /**
   * This element is labeled as a modifier because whether substitution is allow or not, it cannot be ignored.
   */
  allowedCodeableConcept: CodeableConcept;
  /**
   * Indicates the reason for the substitution, or why substitution must or must not be performed.
   */
  reason?: CodeableConcept;
}
/**
 * An order or request for both supply of the medication and the instructions for administration of the medication to a patient. The resource is called "MedicationRequest" rather than "MedicationPrescription" or "MedicationOrder" to generalize the use across inpatient and outpatient settings, including care plans, etc., and to harmonize with workflow patterns.
 */
export interface MedicationRequest extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicationRequest';
  /**
   * The date (and perhaps time) when the prescription was initially written or authored on.
   */
  authoredOn?: string;
  _authoredOn?: Element;
  /**
   * A plan or request that is fulfilled in whole or in part by this medication request.
   */
  basedOn?: Reference[];
  /**
   * The category can be used to include where the medication is expected to be consumed or other types of requests.
   */
  category?: CodeableConcept[];
  /**
   * This attribute should not be confused with the protocol of the medication.
   */
  courseOfTherapyType?: CodeableConcept;
  /**
   * This element can include a detected issue that has been identified either by a decision support system or by a clinician and may include information on the steps that were taken to address the issue.
   */
  detectedIssue?: Reference[];
  /**
   * Indicates the specific details for the dispense or medication supply part of a medication request (also known as a Medication Prescription or Medication Order).  Note that this information is not always sent with the order.  There may be in some settings (e.g. hospitals) institutional or system support for completing the dispense details in the pharmacy department.
   */
  dispenseRequest?: MedicationRequestDispenseRequest;
  /**
   * If do not perform is not specified, the request is a positive request e.g. "do perform".
   */
  doNotPerform?: boolean;
  _doNotPerform?: Element;
  /**
   * There are examples where a medication request may include the option of an oral dose or an Intravenous or Intramuscular dose.  For example, "Ondansetron 8mg orally or IV twice a day as needed for nausea" or "Compazine® (prochlorperazine) 5-10mg PO or 25mg PR bid prn nausea or vomiting".  In these cases, two medication requests would be created that could be grouped together.  The decision on which dose and route of administration to use is based on the patient's condition at the time the dose is needed.
   */
  dosageInstruction?: Dosage[];
  /**
   * This will typically be the encounter the event occurred within, but some activities may be initiated prior to or after the official completion of an encounter but still be tied to the context of the encounter."    If there is a need to link to episodes of care they will be handled with an extension.
   */
  encounter?: Reference;
  /**
   * This might not include provenances for all versions of the request – only those deemed “relevant” or important. This SHALL NOT include the provenance associated with this current version of the resource. (If that provenance is deemed to be a “relevant” change, it will need to be added as part of a later update. Until then, it can be queried directly as the provenance that points to this version using _revinclude All Provenances should have some historical version of this Request as their subject.).
   */
  eventHistory?: Reference[];
  /**
   * A shared identifier common to all requests that were authorized more or less simultaneously by a single author, representing the identifier of the requisition or prescription.
   */
  groupIdentifier?: Identifier;
  /**
   * This is a business identifier, not a resource identifier.
   */
  identifier?: Identifier[];
  /**
   * The URL pointing to a protocol, guideline, orderset, or other definition that is adhered to in whole or in part by this MedicationRequest.
   */
  instantiatesCanonical?: string[];
  _instantiatesCanonical?: Element;
  /**
   * The URL pointing to an externally maintained protocol, guideline, orderset or other definition that is adhered to in whole or in part by this MedicationRequest.
   */
  instantiatesUri?: string[];
  _instantiatesUri?: Element;
  /**
   * Insurance plans, coverage extensions, pre-authorizations and/or pre-determinations that may be required for delivering the requested service.
   */
  insurance?: Reference[];
  /**
   * It is expected that the type of requester will be restricted for different stages of a MedicationRequest.  For example, Proposals can be created by a patient, relatedPerson, Practitioner or Device.  Plans can be created by Practitioners, Patients, RelatedPersons and Devices.  Original orders can be created by a Practitioner only.
   * An instance-order is an instantiation of a request or order and may be used to populate Medication Administration Record.
   * This element is labeled as a modifier because the intent alters when and how the resource is actually applicable.
   */
  intent: string;
  _intent?: Element;
  /**
   * If only a code is specified, then it needs to be a code for a specific product. If more information is required, then the use of the Medication resource is recommended.  For example, if you require form or lot number or if the medication is compounded or extemporaneously prepared, then you must reference the Medication resource.
   */
  medicationCodeableConcept: CodeableConcept;
  /**
   * If only a code is specified, then it needs to be a code for a specific product. If more information is required, then the use of the Medication resource is recommended.  For example, if you require form or lot number or if the medication is compounded or extemporaneously prepared, then you must reference the Medication resource.
   */
  medicationReference: Reference;
  /**
   * Extra information about the prescription that could not be conveyed by the other attributes.
   */
  note?: Annotation[];
  /**
   * The specified desired performer of the medication treatment (e.g. the performer of the medication administration).
   */
  performer?: Reference;
  /**
   * If specified without indicating a performer, this indicates that the performer must be of the specified type. If specified with a performer then it indicates the requirements of the performer if the designated performer is not available.
   */
  performerType?: CodeableConcept;
  /**
   * Indicates how quickly the Medication Request should be addressed with respect to other requests.
   */
  priority?: string;
  _priority?: Element;
  /**
   * A link to a resource representing an earlier order related order or prescription.
   */
  priorPrescription?: Reference;
  /**
   * This could be a diagnosis code. If a full condition record exists or additional detail is needed, use reasonReference.
   */
  reasonCode?: CodeableConcept[];
  /**
   * This is a reference to a condition or observation that is the reason for the medication order.  If only a code exists, use reasonCode.
   */
  reasonReference?: Reference[];
  /**
   * The person who entered the order on behalf of another individual for example in the case of a verbal or a telephone order.
   */
  recorder?: Reference;
  /**
   * Indicates if this record was captured as a secondary 'reported' record rather than as an original primary source-of-truth record.  It may also indicate the source of the report.
   */
  reportedBoolean?: boolean;
  _reportedBoolean?: Element;
  /**
   * Indicates if this record was captured as a secondary 'reported' record rather than as an original primary source-of-truth record.  It may also indicate the source of the report.
   */
  reportedReference?: Reference;
  /**
   * The individual, organization, or device that initiated the request and has responsibility for its activation.
   */
  requester?: Reference;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * This is generally only used for "exception" statuses such as "suspended" or "cancelled". The reason why the MedicationRequest was created at all is captured in reasonCode, not here.
   */
  statusReason?: CodeableConcept;
  /**
   * The subject on a medication request is mandatory.  For the secondary use case where the actual subject is not provided, there still must be an anonymized subject specified.
   */
  subject: Reference;
  /**
   * Indicates whether or not substitution can or should be part of the dispense. In some cases, substitution must happen, in other cases substitution must not happen. This block explains the prescriber's intent. If nothing is specified substitution may be done.
   */
  substitution?: MedicationRequestSubstitution;
  /**
   * Include additional information (for example, patient height and weight) that supports the ordering of the medication.
   */
  supportingInformation?: Reference[];
}
/**
 * Code Values for the MedicationRequest.intent field
 */
export enum MedicationRequestIntentCodes {
  PROPOSAL = 'proposal',
  PLAN = 'plan',
  ORDER = 'order',
  ORIGINAL_ORDER = 'original-order',
  REFLEX_ORDER = 'reflex-order',
  FILLER_ORDER = 'filler-order',
  INSTANCE_ORDER = 'instance-order',
  OPTION = 'option',
}
/**
 * Code Values for the MedicationRequest.priority field
 */
export enum MedicationRequestPriorityCodes {
  ROUTINE = 'routine',
  URGENT = 'urgent',
  ASAP = 'asap',
  STAT = 'stat',
}
/**
 * Code Values for the MedicationRequest.status field
 */
export enum MedicationRequestStatusCodes {
  ACTIVE = 'active',
  ON_HOLD = 'on-hold',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  STOPPED = 'stopped',
  DRAFT = 'draft',
  UNKNOWN = 'unknown',
}
/**
 * A record of a medication that is being consumed by a patient.   A MedicationStatement may indicate that the patient may be taking the medication now or has taken the medication in the past or will be taking the medication in the future.  The source of this information can be the patient, significant other (such as a family member or spouse), or a clinician.  A common scenario where this information is captured is during the history taking process during a patient visit or stay.   The medication information may come from sources such as the patient's memory, from a prescription bottle,  or from a list of medications the patient, clinician or other party maintains.
 * The primary difference between a medication statement and a medication administration is that the medication administration has complete administration information and is based on actual administration information from the person who administered the medication.  A medication statement is often, if not always, less specific.  There is no required date/time when the medication was administered, in fact we only know that a source has reported the patient is taking this medication, where details such as time, quantity, or rate or even medication product may be incomplete or missing or less precise.  As stated earlier, the medication statement information may come from the patient's memory, from a prescription bottle or from a list of medications the patient, clinician or other party maintains.  Medication administration is more formal and is not missing detailed information.
 */
export interface MedicationStatement extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicationStatement';
  /**
   * A plan, proposal or order that is fulfilled in whole or in part by this event.
   */
  basedOn?: Reference[];
  /**
   * Indicates where the medication is expected to be consumed or administered.
   */
  category?: CodeableConcept;
  /**
   * The encounter or episode of care that establishes the context for this MedicationStatement.
   */
  context?: Reference;
  /**
   * The date when the medication statement was asserted by the information source.
   */
  dateAsserted?: string;
  _dateAsserted?: Element;
  /**
   * Likely references would be to MedicationRequest, MedicationDispense, Claim, Observation or QuestionnaireAnswers.  The most common use cases for deriving a MedicationStatement comes from creating a MedicationStatement from a MedicationRequest or from a lab observation or a claim.  it should be noted that the amount of information that is available varies from the type resource that you derive the MedicationStatement from.
   */
  derivedFrom?: Reference[];
  /**
   * The dates included in the dosage on a Medication Statement reflect the dates for a given dose.  For example, "from November 1, 2016 to November 3, 2016, take one tablet daily and from November 4, 2016 to November 7, 2016, take two tablets daily."  It is expected that this specificity may only be populated where the patient brings in their labeled container or where the Medication Statement is derived from a MedicationRequest.
   */
  dosage?: Dosage[];
  /**
   * This attribute reflects the period over which the patient consumed the medication and is expected to be populated on the majority of Medication Statements. If the medication is still being taken at the time the statement is recorded, the "end" date will be omitted.  The date/time attribute supports a variety of dates - year, year/month and exact date.  If something more than this is required, this should be conveyed as text.
   */
  effectiveDateTime?: string;
  _effectiveDateTime?: Element;
  /**
   * This attribute reflects the period over which the patient consumed the medication and is expected to be populated on the majority of Medication Statements. If the medication is still being taken at the time the statement is recorded, the "end" date will be omitted.  The date/time attribute supports a variety of dates - year, year/month and exact date.  If something more than this is required, this should be conveyed as text.
   */
  effectivePeriod?: Period;
  /**
   * This is a business identifier, not a resource identifier.
   */
  identifier?: Identifier[];
  /**
   * The person or organization that provided the information about the taking of this medication. Note: Use derivedFrom when a MedicationStatement is derived from other resources, e.g. Claim or MedicationRequest.
   */
  informationSource?: Reference;
  /**
   * If only a code is specified, then it needs to be a code for a specific product. If more information is required, then the use of the medication resource is recommended.  For example, if you require form or lot number, then you must reference the Medication resource.
   */
  medicationCodeableConcept: CodeableConcept;
  /**
   * If only a code is specified, then it needs to be a code for a specific product. If more information is required, then the use of the medication resource is recommended.  For example, if you require form or lot number, then you must reference the Medication resource.
   */
  medicationReference: Reference;
  /**
   * Provides extra information about the medication statement that is not conveyed by the other attributes.
   */
  note?: Annotation[];
  /**
   * A larger event of which this particular event is a component or step.
   */
  partOf?: Reference[];
  /**
   * This could be a diagnosis code. If a full condition record exists or additional detail is needed, use reasonForUseReference.
   */
  reasonCode?: CodeableConcept[];
  /**
   * This is a reference to a condition that is the reason why the medication is being/was taken.  If only a code exists, use reasonForUseCode.
   */
  reasonReference?: Reference[];
  /**
   * MedicationStatement is a statement at a point in time.  The status is only representative at the point when it was asserted.  The value set for MedicationStatement.status contains codes that assert the status of the use of the medication by the patient (for example, stopped or on hold) as well as codes that assert the status of the medication statement itself (for example, entered in error).
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * This is generally only used for "exception" statuses such as "not-taken", "on-hold", "cancelled" or "entered-in-error". The reason for performing the event at all is captured in reasonCode, not here.
   */
  statusReason?: CodeableConcept[];
  /**
   * The person, animal or group who is/was taking the medication.
   */
  subject: Reference;
}
/**
 * Code Values for the MedicationStatement.status field
 */
export enum MedicationStatementStatusCodes {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  INTENDED = 'intended',
  STOPPED = 'stopped',
  ON_HOLD = 'on-hold',
  UNKNOWN = 'unknown',
  NOT_TAKEN = 'not-taken',
}
/**
 * Coding words or phrases of the name.
 */
export interface MedicinalProductNameNamePart extends BackboneElement {
  /**
   * A fragment of a product name.
   */
  part: string;
  _part?: Element;
  /**
   * Idenifying type for this part of the name (e.g. strength part).
   */
  type: Coding;
}
/**
 * Country where the name applies.
 */
export interface MedicinalProductNameCountryLanguage extends BackboneElement {
  /**
   * Country code for where this name applies.
   */
  country: CodeableConcept;
  /**
   * Jurisdiction code for where this name applies.
   */
  jurisdiction?: CodeableConcept;
  /**
   * Language code for this name.
   */
  language: CodeableConcept;
}
/**
 * The product's name, including full name and possibly coded parts.
 */
export interface MedicinalProductName extends BackboneElement {
  /**
   * Country where the name applies.
   */
  countryLanguage?: MedicinalProductNameCountryLanguage[];
  /**
   * Coding words or phrases of the name.
   */
  namePart?: MedicinalProductNameNamePart[];
  /**
   * The full product name.
   */
  productName: string;
  _productName?: Element;
}
/**
 * An operation applied to the product, for manufacturing or adminsitrative purpose.
 */
export interface MedicinalProductManufacturingBusinessOperation extends BackboneElement {
  /**
   * Regulatory authorization reference number.
   */
  authorisationReferenceNumber?: Identifier;
  /**
   * To indicate if this proces is commercially confidential.
   */
  confidentialityIndicator?: CodeableConcept;
  /**
   * Regulatory authorization date.
   */
  effectiveDate?: string;
  _effectiveDate?: Element;
  /**
   * The manufacturer or establishment associated with the process.
   */
  manufacturer?: Reference[];
  /**
   * The type of manufacturing operation.
   */
  operationType?: CodeableConcept;
  /**
   * A regulator which oversees the operation.
   */
  regulator?: Reference;
}
/**
 * Indicates if the medicinal product has an orphan designation for the treatment of a rare disease.
 */
export interface MedicinalProductSpecialDesignation extends BackboneElement {
  /**
   * Date when the designation was granted.
   */
  date?: string;
  _date?: Element;
  /**
   * Identifier for the designation, or procedure number.
   */
  identifier?: Identifier[];
  /**
   * Condition for which the medicinal use applies.
   */
  indicationCodeableConcept?: CodeableConcept;
  /**
   * Condition for which the medicinal use applies.
   */
  indicationReference?: Reference;
  /**
   * The intended use of the product, e.g. prevention, treatment.
   */
  intendedUse?: CodeableConcept;
  /**
   * Animal species for which this applies.
   */
  species?: CodeableConcept;
  /**
   * For example granted, pending, expired or withdrawn.
   */
  status?: CodeableConcept;
  /**
   * The type of special designation, e.g. orphan drug, minor use.
   */
  type?: CodeableConcept;
}
/**
 * Detailed definition of a medicinal product, typically for uses other than direct patient care (e.g. regulatory use).
 */
export interface MedicinalProduct extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicinalProduct';
  /**
   * Whether the Medicinal Product is subject to additional monitoring for regulatory reasons.
   */
  additionalMonitoringIndicator?: CodeableConcept;
  /**
   * Supporting documentation, typically for regulatory submission.
   */
  attachedDocument?: Reference[];
  /**
   * Clinical trials or studies that this product is involved in.
   */
  clinicalTrial?: Reference[];
  /**
   * The dose form for a single part product, or combined form of a multiple part product.
   */
  combinedPharmaceuticalDoseForm?: CodeableConcept;
  /**
   * A product specific contact, person (in a role), or an organization.
   */
  contact?: Reference[];
  /**
   * Reference to another product, e.g. for linking authorised to investigational product.
   */
  crossReference?: Identifier[];
  /**
   * If this medicine applies to human or veterinary uses.
   */
  domain?: Coding;
  /**
   * Business identifier for this product. Could be an MPID.
   */
  identifier?: Identifier[];
  /**
   * The legal status of supply of the medicinal product as classified by the regulator.
   */
  legalStatusOfSupply?: CodeableConcept;
  /**
   * An operation applied to the product, for manufacturing or adminsitrative purpose.
   */
  manufacturingBusinessOperation?: MedicinalProductManufacturingBusinessOperation[];
  /**
   * Marketing status of the medicinal product, in contrast to marketing authorizaton.
   */
  marketingStatus?: MarketingStatus[];
  /**
   * A master file for to the medicinal product (e.g. Pharmacovigilance System Master File).
   */
  masterFile?: Reference[];
  /**
   * The product's name, including full name and possibly coded parts.
   */
  name: MedicinalProductName[];
  /**
   * Package representation for the product.
   */
  packagedMedicinalProduct?: Reference[];
  /**
   * If authorised for use in children.
   */
  paediatricUseIndicator?: CodeableConcept;
  /**
   * Pharmaceutical aspects of product.
   */
  pharmaceuticalProduct?: Reference[];
  /**
   * Allows the product to be classified by various systems.
   */
  productClassification?: CodeableConcept[];
  /**
   * Indicates if the medicinal product has an orphan designation for the treatment of a rare disease.
   */
  specialDesignation?: MedicinalProductSpecialDesignation[];
  /**
   * Whether the Medicinal Product is subject to special measures for regulatory reasons.
   */
  specialMeasures?: string[];
  _specialMeasures?: Element;
  /**
   * Regulatory type, e.g. Investigational or Authorized.
   */
  type?: CodeableConcept;
}
/**
 * Authorization in areas within a country.
 */
export interface MedicinalProductAuthorizationJurisdictionalAuthorization extends BackboneElement {
  /**
   * Country of authorization.
   */
  country?: CodeableConcept;
  /**
   * The assigned number for the marketing authorization.
   */
  identifier?: Identifier[];
  /**
   * Jurisdiction within a country.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * The legal status of supply in a jurisdiction or region.
   */
  legalStatusOfSupply?: CodeableConcept;
  /**
   * The start and expected end date of the authorization.
   */
  validityPeriod?: Period;
}
/**
 * The regulatory procedure for granting or amending a marketing authorization.
 */
export interface MedicinalProductAuthorizationProcedure extends BackboneElement {
  /**
   * Applcations submitted to obtain a marketing authorization.
   */
  application?: MedicinalProductAuthorizationProcedure[];
  /**
   * Date of procedure.
   */
  datePeriod?: Period;
  /**
   * Date of procedure.
   */
  dateDateTime?: string;
  _dateDateTime?: Element;
  /**
   * Identifier for this procedure.
   */
  identifier?: Identifier;
  /**
   * Type of procedure.
   */
  type: CodeableConcept;
}
/**
 * The regulatory authorization of a medicinal product.
 */
export interface MedicinalProductAuthorization extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicinalProductAuthorization';
  /**
   * The country in which the marketing authorization has been granted.
   */
  country?: CodeableConcept[];
  /**
   * A period of time after authorization before generic product applicatiosn can be submitted.
   */
  dataExclusivityPeriod?: Period;
  /**
   * The date when the first authorization was granted by a Medicines Regulatory Agency.
   */
  dateOfFirstAuthorization?: string;
  _dateOfFirstAuthorization?: Element;
  /**
   * Marketing Authorization Holder.
   */
  holder?: Reference;
  /**
   * Business identifier for the marketing authorization, as assigned by a regulator.
   */
  identifier?: Identifier[];
  /**
   * Date of first marketing authorization for a company's new medicinal product in any country in the World.
   */
  internationalBirthDate?: string;
  _internationalBirthDate?: Element;
  /**
   * Jurisdiction within a country.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * Authorization in areas within a country.
   */
  jurisdictionalAuthorization?: MedicinalProductAuthorizationJurisdictionalAuthorization[];
  /**
   * The legal framework against which this authorization is granted.
   */
  legalBasis?: CodeableConcept;
  /**
   * The regulatory procedure for granting or amending a marketing authorization.
   */
  procedure?: MedicinalProductAuthorizationProcedure;
  /**
   * Medicines Regulatory Agency.
   */
  regulator?: Reference;
  /**
   * The date when a suspended the marketing or the marketing authorization of the product is anticipated to be restored.
   */
  restoreDate?: string;
  _restoreDate?: Element;
  /**
   * The status of the marketing authorization.
   */
  status?: CodeableConcept;
  /**
   * The date at which the given status has become applicable.
   */
  statusDate?: string;
  _statusDate?: Element;
  /**
   * The medicinal product that is being authorized.
   */
  subject?: Reference;
  /**
   * The beginning of the time period in which the marketing authorization is in the specific status shall be specified A complete date consisting of day, month and year shall be specified using the ISO 8601 date format.
   */
  validityPeriod?: Period;
}
/**
 * Information about the use of the medicinal product in relation to other therapies described as part of the indication.
 */
export interface MedicinalProductContraindicationOtherTherapy extends BackboneElement {
  /**
   * Reference to a specific medication (active substance, medicinal product or class of products) as part of an indication or contraindication.
   */
  medicationCodeableConcept: CodeableConcept;
  /**
   * Reference to a specific medication (active substance, medicinal product or class of products) as part of an indication or contraindication.
   */
  medicationReference: Reference;
  /**
   * The type of relationship between the medicinal product indication or contraindication and another therapy.
   */
  therapyRelationshipType: CodeableConcept;
}
/**
 * The clinical particulars - indications, contraindications etc. of a medicinal product, including for regulatory purposes.
 */
export interface MedicinalProductContraindication extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicinalProductContraindication';
  /**
   * A comorbidity (concurrent condition) or coinfection.
   */
  comorbidity?: CodeableConcept[];
  /**
   * The disease, symptom or procedure for the contraindication.
   */
  disease?: CodeableConcept;
  /**
   * The status of the disease or symptom for the contraindication.
   */
  diseaseStatus?: CodeableConcept;
  /**
   * Information about the use of the medicinal product in relation to other therapies described as part of the indication.
   */
  otherTherapy?: MedicinalProductContraindicationOtherTherapy[];
  /**
   * The population group to which this applies.
   */
  population?: Population[];
  /**
   * The medication for which this is an indication.
   */
  subject?: Reference[];
  /**
   * Information about the use of the medicinal product in relation to other therapies as part of the indication.
   */
  therapeuticIndication?: Reference[];
}
/**
 * Information about the use of the medicinal product in relation to other therapies described as part of the indication.
 */
export interface MedicinalProductIndicationOtherTherapy extends BackboneElement {
  /**
   * Reference to a specific medication (active substance, medicinal product or class of products) as part of an indication or contraindication.
   */
  medicationCodeableConcept: CodeableConcept;
  /**
   * Reference to a specific medication (active substance, medicinal product or class of products) as part of an indication or contraindication.
   */
  medicationReference: Reference;
  /**
   * The type of relationship between the medicinal product indication or contraindication and another therapy.
   */
  therapyRelationshipType: CodeableConcept;
}
/**
 * Indication for the Medicinal Product.
 */
export interface MedicinalProductIndication extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicinalProductIndication';
  /**
   * Comorbidity (concurrent condition) or co-infection as part of the indication.
   */
  comorbidity?: CodeableConcept[];
  /**
   * The status of the disease or symptom for which the indication applies.
   */
  diseaseStatus?: CodeableConcept;
  /**
   * The disease, symptom or procedure that is the indication for treatment.
   */
  diseaseSymptomProcedure?: CodeableConcept;
  /**
   * Timing or duration information as part of the indication.
   */
  duration?: Quantity;
  /**
   * The intended effect, aim or strategy to be achieved by the indication.
   */
  intendedEffect?: CodeableConcept;
  /**
   * Information about the use of the medicinal product in relation to other therapies described as part of the indication.
   */
  otherTherapy?: MedicinalProductIndicationOtherTherapy[];
  /**
   * The population group to which this applies.
   */
  population?: Population[];
  /**
   * The medication for which this is an indication.
   */
  subject?: Reference[];
  /**
   * Describe the undesirable effects of the medicinal product.
   */
  undesirableEffect?: Reference[];
}
/**
 * Strength expressed in terms of a reference substance.
 */
export interface MedicinalProductIngredientSpecifiedSubstanceStrengthReferenceStrength extends BackboneElement {
  /**
   * The country or countries for which the strength range applies.
   */
  country?: CodeableConcept[];
  /**
   * For when strength is measured at a particular point or distance.
   */
  measurementPoint?: string;
  _measurementPoint?: Element;
  /**
   * Strength expressed in terms of a reference substance.
   */
  strength: Ratio;
  /**
   * Strength expressed in terms of a reference substance.
   */
  strengthLowLimit?: Ratio;
  /**
   * Relevant reference substance.
   */
  substance?: CodeableConcept;
}
/**
 * Quantity of the substance or specified substance present in the manufactured item or pharmaceutical product.
 */
export interface MedicinalProductIngredientSpecifiedSubstanceStrength extends BackboneElement {
  /**
   * The strength per unitary volume (or mass).
   */
  concentration?: Ratio;
  /**
   * A lower limit for the strength per unitary volume (or mass), for when there is a range. The concentration attribute then becomes the upper limit.
   */
  concentrationLowLimit?: Ratio;
  /**
   * The country or countries for which the strength range applies.
   */
  country?: CodeableConcept[];
  /**
   * For when strength is measured at a particular point or distance.
   */
  measurementPoint?: string;
  _measurementPoint?: Element;
  /**
   * The quantity of substance in the unit of presentation, or in the volume (or mass) of the single pharmaceutical product or manufactured item.
   */
  presentation: Ratio;
  /**
   * A lower limit for the quantity of substance in the unit of presentation. For use when there is a range of strengths, this is the lower limit, with the presentation attribute becoming the upper limit.
   */
  presentationLowLimit?: Ratio;
  /**
   * Strength expressed in terms of a reference substance.
   */
  referenceStrength?: MedicinalProductIngredientSpecifiedSubstanceStrengthReferenceStrength[];
}
/**
 * A specified substance that comprises this ingredient.
 */
export interface MedicinalProductIngredientSpecifiedSubstance extends BackboneElement {
  /**
   * The specified substance.
   */
  code: CodeableConcept;
  /**
   * Confidentiality level of the specified substance as the ingredient.
   */
  confidentiality?: CodeableConcept;
  /**
   * The group of specified substance, e.g. group 1 to 4.
   */
  group: CodeableConcept;
  /**
   * Quantity of the substance or specified substance present in the manufactured item or pharmaceutical product.
   */
  strength?: MedicinalProductIngredientSpecifiedSubstanceStrength[];
}
/**
 * The ingredient substance.
 */
export interface MedicinalProductIngredientSubstance extends BackboneElement {
  /**
   * The ingredient substance.
   */
  code: CodeableConcept;
  /**
   * Quantity of the substance or specified substance present in the manufactured item or pharmaceutical product.
   */
  strength?: MedicinalProductIngredientSpecifiedSubstanceStrength[];
}
/**
 * An ingredient of a manufactured item or pharmaceutical product.
 */
export interface MedicinalProductIngredient extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicinalProductIngredient';
  /**
   * If the ingredient is a known or suspected allergen.
   */
  allergenicIndicator?: boolean;
  _allergenicIndicator?: Element;
  /**
   * The identifier(s) of this Ingredient that are assigned by business processes and/or used to refer to it when a direct URL reference to the resource itself is not appropriate.
   */
  identifier?: Identifier;
  /**
   * Manufacturer of this Ingredient.
   */
  manufacturer?: Reference[];
  /**
   * Ingredient role e.g. Active ingredient, excipient.
   */
  role: CodeableConcept;
  /**
   * A specified substance that comprises this ingredient.
   */
  specifiedSubstance?: MedicinalProductIngredientSpecifiedSubstance[];
  /**
   * The ingredient substance.
   */
  substance?: MedicinalProductIngredientSubstance;
}
/**
 * The specific medication, food or laboratory test that interacts.
 */
export interface MedicinalProductInteractionInteractant extends BackboneElement {
  /**
   * The specific medication, food or laboratory test that interacts.
   */
  itemReference: Reference;
  /**
   * The specific medication, food or laboratory test that interacts.
   */
  itemCodeableConcept: CodeableConcept;
}
/**
 * The interactions of the medicinal product with other medicinal products, or other forms of interactions.
 */
export interface MedicinalProductInteraction extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicinalProductInteraction';
  /**
   * The interaction described.
   */
  description?: string;
  _description?: Element;
  /**
   * The effect of the interaction, for example "reduced gastric absorption of primary medication".
   */
  effect?: CodeableConcept;
  /**
   * The incidence of the interaction, e.g. theoretical, observed.
   */
  incidence?: CodeableConcept;
  /**
   * The specific medication, food or laboratory test that interacts.
   */
  interactant?: MedicinalProductInteractionInteractant[];
  /**
   * Actions for managing the interaction.
   */
  management?: CodeableConcept;
  /**
   * The medication for which this is a described interaction.
   */
  subject?: Reference[];
  /**
   * The type of the interaction e.g. drug-drug interaction, drug-food interaction, drug-lab test interaction.
   */
  type?: CodeableConcept;
}
/**
 * The manufactured item as contained in the packaged medicinal product.
 */
export interface MedicinalProductManufactured extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicinalProductManufactured';
  /**
   * Ingredient.
   */
  ingredient?: Reference[];
  /**
   * Dose form as manufactured and before any transformation into the pharmaceutical product.
   */
  manufacturedDoseForm: CodeableConcept;
  /**
   * Manufacturer of the item (Note that this should be named "manufacturer" but it currently causes technical issues).
   */
  manufacturer?: Reference[];
  /**
   * Other codeable characteristics.
   */
  otherCharacteristics?: CodeableConcept[];
  /**
   * Dimensions, color etc.
   */
  physicalCharacteristics?: ProdCharacteristic;
  /**
   * The quantity or "count number" of the manufactured item.
   */
  quantity: Quantity;
  /**
   * The “real world” units in which the quantity of the manufactured item is described.
   */
  unitOfPresentation?: CodeableConcept;
}
/**
 * Batch numbering.
 */
export interface MedicinalProductPackagedBatchIdentifier extends BackboneElement {
  /**
   * A number appearing on the immediate packaging (and not the outer packaging).
   */
  immediatePackaging?: Identifier;
  /**
   * A number appearing on the outer packaging of a specific batch.
   */
  outerPackaging: Identifier;
}
/**
 * A packaging item, as a contained for medicine, possibly with other packaging items within.
 */
export interface MedicinalProductPackagedPackageItem extends BackboneElement {
  /**
   * A possible alternate material for the packaging.
   */
  alternateMaterial?: CodeableConcept[];
  /**
   * A device accompanying a medicinal product.
   */
  device?: Reference[];
  /**
   * Including possibly Data Carrier Identifier.
   */
  identifier?: Identifier[];
  /**
   * The manufactured item as contained in the packaged medicinal product.
   */
  manufacturedItem?: Reference[];
  /**
   * Manufacturer of this Package Item.
   */
  manufacturer?: Reference[];
  /**
   * Material type of the package item.
   */
  material?: CodeableConcept[];
  /**
   * Other codeable characteristics.
   */
  otherCharacteristics?: CodeableConcept[];
  /**
   * Allows containers within containers.
   */
  packageItem?: MedicinalProductPackagedPackageItem[];
  /**
   * Dimensions, color etc.
   */
  physicalCharacteristics?: ProdCharacteristic;
  /**
   * The quantity of this package in the medicinal product, at the current level of packaging. The outermost is always 1.
   */
  quantity: Quantity;
  /**
   * Shelf Life and storage information.
   */
  shelfLifeStorage?: ProductShelfLife[];
  /**
   * The physical type of the container of the medicine.
   */
  type: CodeableConcept;
}
/**
 * A medicinal product in a container or package.
 */
export interface MedicinalProductPackaged extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicinalProductPackaged';
  /**
   * Batch numbering.
   */
  batchIdentifier?: MedicinalProductPackagedBatchIdentifier[];
  /**
   * Textual description.
   */
  description?: string;
  _description?: Element;
  /**
   * Unique identifier.
   */
  identifier?: Identifier[];
  /**
   * The legal status of supply of the medicinal product as classified by the regulator.
   */
  legalStatusOfSupply?: CodeableConcept;
  /**
   * Manufacturer of this Package Item.
   */
  manufacturer?: Reference[];
  /**
   * Manufacturer of this Package Item.
   */
  marketingAuthorization?: Reference;
  /**
   * Marketing information.
   */
  marketingStatus?: MarketingStatus[];
  /**
   * A packaging item, as a contained for medicine, possibly with other packaging items within.
   */
  packageItem: MedicinalProductPackagedPackageItem[];
  /**
   * The product with this is a pack for.
   */
  subject?: Reference[];
}
/**
 * Characteristics e.g. a products onset of action.
 */
export interface MedicinalProductPharmaceuticalCharacteristics extends BackboneElement {
  /**
   * A coded characteristic.
   */
  code: CodeableConcept;
  /**
   * The status of characteristic e.g. assigned or pending.
   */
  status?: CodeableConcept;
}
/**
 * A species specific time during which consumption of animal product is not appropriate.
 */
export interface MedicinalProductPharmaceuticalRouteOfAdministrationTargetSpeciesWithdrawalPeriod extends BackboneElement {
  /**
   * Extra information about the withdrawal period.
   */
  supportingInformation?: string;
  _supportingInformation?: Element;
  /**
   * Coded expression for the type of tissue for which the withdrawal period applues, e.g. meat, milk.
   */
  tissue: CodeableConcept;
  /**
   * A value for the time.
   */
  value: Quantity;
}
/**
 * A species for which this route applies.
 */
export interface MedicinalProductPharmaceuticalRouteOfAdministrationTargetSpecies extends BackboneElement {
  /**
   * Coded expression for the species.
   */
  code: CodeableConcept;
  /**
   * A species specific time during which consumption of animal product is not appropriate.
   */
  withdrawalPeriod?: MedicinalProductPharmaceuticalRouteOfAdministrationTargetSpeciesWithdrawalPeriod[];
}
/**
 * The path by which the pharmaceutical product is taken into or makes contact with the body.
 */
export interface MedicinalProductPharmaceuticalRouteOfAdministration extends BackboneElement {
  /**
   * Coded expression for the route.
   */
  code: CodeableConcept;
  /**
   * The first dose (dose quantity) administered in humans can be specified, for a product under investigation, using a numerical value and its unit of measurement.
   */
  firstDose?: Quantity;
  /**
   * The maximum dose per day (maximum dose quantity to be administered in any one 24-h period) that can be administered as per the protocol referenced in the clinical trial authorisation.
   */
  maxDosePerDay?: Quantity;
  /**
   * The maximum dose per treatment period that can be administered as per the protocol referenced in the clinical trial authorisation.
   */
  maxDosePerTreatmentPeriod?: Ratio;
  /**
   * The maximum single dose that can be administered as per the protocol of a clinical trial can be specified using a numerical value and its unit of measurement.
   */
  maxSingleDose?: Quantity;
  /**
   * The maximum treatment period during which an Investigational Medicinal Product can be administered as per the protocol referenced in the clinical trial authorisation.
   */
  maxTreatmentPeriod?: Duration;
  /**
   * A species for which this route applies.
   */
  targetSpecies?: MedicinalProductPharmaceuticalRouteOfAdministrationTargetSpecies[];
}
/**
 * A pharmaceutical product described in terms of its composition and dose form.
 */
export interface MedicinalProductPharmaceutical extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicinalProductPharmaceutical';
  /**
   * The administrable dose form, after necessary reconstitution.
   */
  administrableDoseForm: CodeableConcept;
  /**
   * Characteristics e.g. a products onset of action.
   */
  characteristics?: MedicinalProductPharmaceuticalCharacteristics[];
  /**
   * Accompanying device.
   */
  device?: Reference[];
  /**
   * An identifier for the pharmaceutical medicinal product.
   */
  identifier?: Identifier[];
  /**
   * Ingredient.
   */
  ingredient?: Reference[];
  /**
   * The path by which the pharmaceutical product is taken into or makes contact with the body.
   */
  routeOfAdministration: MedicinalProductPharmaceuticalRouteOfAdministration[];
  /**
   * Todo.
   */
  unitOfPresentation?: CodeableConcept;
}
/**
 * Describe the undesirable effects of the medicinal product.
 */
export interface MedicinalProductUndesirableEffect extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MedicinalProductUndesirableEffect';
  /**
   * Classification of the effect.
   */
  classification?: CodeableConcept;
  /**
   * The frequency of occurrence of the effect.
   */
  frequencyOfOccurrence?: CodeableConcept;
  /**
   * The population group to which this applies.
   */
  population?: Population[];
  /**
   * The medication for which this is an indication.
   */
  subject?: Reference[];
  /**
   * The symptom, condition or undesirable effect.
   */
  symptomConditionEffect?: CodeableConcept;
}
/**
 * Identifies the resource (or resources) that are being addressed by the event.  For example, the Encounter for an admit message or two Account records for a merge.
 */
export interface MessageDefinitionFocus extends BackboneElement {
  /**
   * Multiple focuses addressing different resources may occasionally occur.  E.g. to link or unlink a resource from a particular account or encounter, etc.
   */
  code: string;
  _code?: Element;
  /**
   * Identifies the maximum number of resources of this type that must be pointed to by a message in order for it to be valid against this MessageDefinition.
   */
  max?: string;
  _max?: Element;
  /**
   * Identifies the minimum number of resources of this type that must be pointed to by a message in order for it to be valid against this MessageDefinition.
   */
  min: number;
  /**
   * This should be present for most message definitions.  However, if the message focus is only a single resource and there is no need to include referenced resources or otherwise enforce the presence of particular elements, it can be omitted.
   */
  profile?: string;
  _profile?: Element;
}
/**
 * This indicates an application level response to "close" a transaction implicit in a particular request message.  To define a complete workflow scenario, look to the [[PlanDefinition]] resource which allows the definition of complex orchestrations, conditionality, etc.
 */
export interface MessageDefinitionAllowedResponse extends BackboneElement {
  /**
   * A reference to the message definition that must be adhered to by this supported response.
   */
  message: string;
  _message?: Element;
  /**
   * Provides a description of the circumstances in which this response should be used (as opposed to one of the alternative responses).
   */
  situation?: string;
  _situation?: Element;
}
/**
 * Defines the characteristics of a message that can be shared between systems, including the type of event that initiates the message, the content to be transmitted and what response(s), if any, are permitted.
 */
export interface MessageDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MessageDefinition';
  /**
   * This indicates an application level response to "close" a transaction implicit in a particular request message.  To define a complete workflow scenario, look to the [[PlanDefinition]] resource which allows the definition of complex orchestrations, conditionality, etc.
   */
  allowedResponse?: MessageDefinitionAllowedResponse[];
  /**
   * The MessageDefinition that is the basis for the contents of this resource.
   */
  base?: string;
  _base?: Element;
  /**
   * The impact of the content of the message.
   */
  category?: string;
  _category?: Element;
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the message definition and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the message definition.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the message definition. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the message definition was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the message definition as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the message definition is presumed to be the predominant language in the place the message definition was created).
   */
  description?: string;
  _description?: Element;
  /**
   * Event code or link to the EventDefinition.
   */
  eventCoding: Coding;
  /**
   * Event code or link to the EventDefinition.
   */
  eventUri: string;
  _eventUri?: Element;
  /**
   * Allows filtering of message definitions that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Identifies the resource (or resources) that are being addressed by the event.  For example, the Encounter for an admit message or two Account records for a merge.
   */
  focus?: MessageDefinitionFocus[];
  /**
   * Canonical reference to a GraphDefinition. If a URL is provided, it is the canonical reference to a [GraphDefinition](graphdefinition.html) that it controls what resources are to be added to the bundle when building the document. The GraphDefinition can also specify profiles that apply to the various resources.
   */
  graph?: string[];
  _graph?: Element;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this message definition outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * It may be possible for the message definition to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * It should be possible to use MessageDefinition to describe a message to be used by certain steps in a particular protocol as part of a PlanDefinition or ActivityDefinition.
   */
  parent?: string[];
  _parent?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the message definition is the organization or individual primarily responsible for the maintenance and upkeep of the message definition. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the message definition. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the message definition. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this message definition.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * A MessageDefinition that is superseded by this definition.
   */
  replaces?: string[];
  _replaces?: Element;
  /**
   * This enables the capability currently available through MSH-16 (Application Level acknowledgement) in HL7 Version 2 to declare at a message instance level whether a response is required or only upon error or success, or never.
   */
  responseRequired?: string;
  _responseRequired?: Element;
  /**
   * Allows filtering of message definitions that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different message definition instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the message definition with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the MessageDefinition.category field
 */
export enum MessageDefinitionCategoryCodes {
  CONSEQUENCE = 'consequence',
  CURRENCY = 'currency',
  NOTIFICATION = 'notification',
}
/**
 * Code Values for the MessageDefinition.responseRequired field
 */
export enum MessageDefinitionResponseRequiredCodes {
  ALWAYS = 'always',
  ON_ERROR = 'on-error',
  NEVER = 'never',
  ON_SUCCESS = 'on-success',
}
/**
 * Code Values for the MessageDefinition.status field
 */
export enum MessageDefinitionStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * There SHOULD be at least one destination, but in some circumstances, the source system is unaware of any particular destination system.
 */
export interface MessageHeaderDestination extends BackboneElement {
  /**
   * The id may be a non-resolvable URI for systems that do not use standard network-based addresses.
   */
  endpoint: string;
  _endpoint?: Element;
  /**
   * Human-readable name for the target system.
   */
  name?: string;
  _name?: Element;
  /**
   * Allows data conveyed by a message to be addressed to a particular person or department when routing to a specific application isn't sufficient.
   */
  receiver?: Reference;
  /**
   * Identifies the target end system in situations where the initial message transmission is to an intermediary system.
   */
  target?: Reference;
}
/**
 * The source application from which this message originated.
 */
export interface MessageHeaderSource extends BackboneElement {
  /**
   * An e-mail, phone, website or other contact point to use to resolve issues with message communications.
   */
  contact?: ContactPoint;
  /**
   * The id may be a non-resolvable URI for systems that do not use standard network-based addresses.
   */
  endpoint: string;
  _endpoint?: Element;
  /**
   * Human-readable name for the source system.
   */
  name?: string;
  _name?: Element;
  /**
   * May include configuration or other information useful in debugging.
   */
  software?: string;
  _software?: Element;
  /**
   * Can convey versions of multiple systems in situations where a message passes through multiple hands.
   */
  version?: string;
  _version?: Element;
}
/**
 * Information about the message that this message is a response to.  Only present if this message is a response.
 */
export interface MessageHeaderResponse extends BackboneElement {
  /**
   * This is a generic response to the request message. Specific data for the response will be found in MessageHeader.focus.
   */
  code: string;
  _code?: Element;
  /**
   * This SHALL be contained in the bundle. If any of the issues are errors, the response code SHALL be an error.
   */
  details?: Reference;
  /**
   * The MessageHeader.id of the message to which this message is a response.
   */
  identifier: string;
  _identifier?: Element;
}
/**
 * Code Values for the MessageHeader.response.code field
 */
export enum MessageHeaderResponseCodeCodes {
  OK = 'ok',
  TRANSIENT_ERROR = 'transient-error',
  FATAL_ERROR = 'fatal-error',
}
/**
 * The header for a message exchange that is either requesting or responding to an action.  The reference(s) that are the subject of the action as well as other information related to the action are typically transmitted in a bundle in which the MessageHeader resource instance is the first resource in the bundle.
 */
export interface MessageHeader extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MessageHeader';
  /**
   * Usually only for the request but can be used in a response.
   */
  author?: Reference;
  /**
   * Permanent link to the MessageDefinition for this message.
   */
  definition?: string;
  _definition?: Element;
  /**
   * There SHOULD be at least one destination, but in some circumstances, the source system is unaware of any particular destination system.
   */
  destination?: MessageHeaderDestination[];
  /**
   * Usually only for the request but can be used in a response.
   */
  enterer?: Reference;
  /**
   * The time of the event will be found in the focus resource. The time of the message will be found in [Bundle.timestamp](bundle-definitions.html#Bundle.timestamp).
   */
  eventCoding: Coding;
  /**
   * The time of the event will be found in the focus resource. The time of the message will be found in [Bundle.timestamp](bundle-definitions.html#Bundle.timestamp).
   */
  eventUri: string;
  _eventUri?: Element;
  /**
   * The data is defined where the transaction type is defined. The transaction data is always included in the bundle that is the full message.  Only the root resource is specified.  The resources it references should be contained in the bundle but are not also listed here.  Multiple repetitions are allowed to cater for merges and other situations with multiple focal targets.
   */
  focus?: Reference[];
  /**
   * Coded indication of the cause for the event - indicates  a reason for the occurrence of the event that is a focus of this message.
   */
  reason?: CodeableConcept;
  /**
   * Information about the message that this message is a response to.  Only present if this message is a response.
   */
  response?: MessageHeaderResponse;
  /**
   * Usually only for the request but can be used in a response.
   */
  responsible?: Reference;
  /**
   * Use case is for where a (trusted) sending system is responsible for multiple organizations, and therefore cannot differentiate based on source endpoint / authentication alone.
   */
  sender?: Reference;
  /**
   * The source application from which this message originated.
   */
  source: MessageHeaderSource;
}
/**
 * A sequence that is used as a reference to describe variants that are present in a sequence analyzed.
 */
export interface MolecularSequenceReferenceSeq extends BackboneElement {
  /**
   * Structural unit composed of a nucleic acid molecule which controls its own replication through the interaction of specific proteins at one or more origins of replication ([SO:0000340](http://www.sequenceontology.org/browser/current_svn/term/SO:0000340)).
   */
  chromosome?: CodeableConcept;
  /**
   * The Genome Build used for reference, following GRCh build versions e.g. 'GRCh 37'.  Version number must be included if a versioned release of a primary build was used.
   */
  genomeBuild?: string;
  _genomeBuild?: Element;
  /**
   * A relative reference to a DNA strand based on gene orientation. The strand that contains the open reading frame of the gene is the "sense" strand, and the opposite complementary strand is the "antisense" strand.
   */
  orientation?: string;
  _orientation?: Element;
  /**
   * Reference identifier of reference sequence submitted to NCBI. It must match the type in the MolecularSequence.type field. For example, the prefix, “NG_” identifies reference sequence for genes, “NM_” for messenger RNA transcripts, and “NP_” for amino acid sequences.
   */
  referenceSeqId?: CodeableConcept;
  /**
   * A pointer to another MolecularSequence entity as reference sequence.
   */
  referenceSeqPointer?: Reference;
  /**
   * A string like "ACGT".
   */
  referenceSeqString?: string;
  _referenceSeqString?: Element;
  /**
   * An absolute reference to a strand. The Watson strand is the strand whose 5'-end is on the short arm of the chromosome, and the Crick strand as the one whose 5'-end is on the long arm.
   */
  strand?: string;
  _strand?: Element;
  /**
   * End position of the window on the reference sequence. If the coordinate system is 0-based then end is exclusive and does not include the last position. If the coordinate system is 1-base, then end is inclusive and includes the last position.
   */
  windowEnd?: number;
  /**
   * Start position of the window on the reference sequence. If the coordinate system is either 0-based or 1-based, then start position is inclusive.
   */
  windowStart?: number;
}
/**
 * Code Values for the MolecularSequence.referenceSeq.orientation field
 */
export enum MolecularSequenceReferenceSeqOrientationCodes {
  SENSE = 'sense',
  ANTISENSE = 'antisense',
}
/**
 * Code Values for the MolecularSequence.referenceSeq.strand field
 */
export enum MolecularSequenceReferenceSeqStrandCodes {
  WATSON = 'watson',
  CRICK = 'crick',
}
/**
 * The definition of variant here originates from Sequence ontology ([variant_of](http://www.sequenceontology.org/browser/current_svn/term/variant_of)). This element can represent amino acid or nucleic sequence change(including insertion,deletion,SNP,etc.)  It can represent some complex mutation or segment variation with the assist of CIGAR string.
 */
export interface MolecularSequenceVariant extends BackboneElement {
  /**
   * Extended CIGAR string for aligning the sequence with reference bases. See detailed documentation [here](http://support.illumina.com/help/SequencingAnalysisWorkflow/Content/Vault/Informatics/Sequencing_Analysis/CASAVA/swSEQ_mCA_ExtendedCIGARFormat.htm).
   */
  cigar?: string;
  _cigar?: Element;
  /**
   * End position of the variant on the reference sequence. If the coordinate system is 0-based then end is exclusive and does not include the last position. If the coordinate system is 1-base, then end is inclusive and includes the last position.
   */
  end?: number;
  /**
   * An allele is one of a set of coexisting sequence variants of a gene ([SO:0001023](http://www.sequenceontology.org/browser/current_svn/term/SO:0001023)).  Nucleotide(s)/amino acids from start position of sequence to stop position of sequence on the positive (+) strand of the observed  sequence. When the sequence  type is DNA, it should be the sequence on the positive (+) strand. This will lay in the range between variant.start and variant.end.
   */
  observedAllele?: string;
  _observedAllele?: Element;
  /**
   * An allele is one of a set of coexisting sequence variants of a gene ([SO:0001023](http://www.sequenceontology.org/browser/current_svn/term/SO:0001023)). Nucleotide(s)/amino acids from start position of sequence to stop position of sequence on the positive (+) strand of the reference sequence. When the sequence  type is DNA, it should be the sequence on the positive (+) strand. This will lay in the range between variant.start and variant.end.
   */
  referenceAllele?: string;
  _referenceAllele?: Element;
  /**
   * Start position of the variant on the  reference sequence. If the coordinate system is either 0-based or 1-based, then start position is inclusive.
   */
  start?: number;
  /**
   * A pointer to an Observation containing variant information.
   */
  variantPointer?: Reference;
}
/**
 * Receiver Operator Characteristic (ROC) Curve  to give sensitivity/specificity tradeoff.
 */
export interface MolecularSequenceQualityRoc extends BackboneElement {
  /**
   * Calculated fScore if the GQ score threshold was set to "score" field value.
   */
  fMeasure?: number[];
  /**
   * The number of false negatives if the GQ score threshold was set to "score" field value.
   */
  numFN?: number[];
  /**
   * The number of false positives if the GQ score threshold was set to "score" field value.
   */
  numFP?: number[];
  /**
   * The number of true positives if the GQ score threshold was set to "score" field value.
   */
  numTP?: number[];
  /**
   * Calculated precision if the GQ score threshold was set to "score" field value.
   */
  precision?: number[];
  /**
   * Invidual data point representing the GQ (genotype quality) score threshold.
   */
  score?: number[];
  /**
   * Calculated sensitivity if the GQ score threshold was set to "score" field value.
   */
  sensitivity?: number[];
}
/**
 * An experimental feature attribute that defines the quality of the feature in a quantitative way, such as a phred quality score ([SO:0001686](http://www.sequenceontology.org/browser/current_svn/term/SO:0001686)).
 */
export interface MolecularSequenceQuality extends BackboneElement {
  /**
   * End position of the sequence. If the coordinate system is 0-based then end is exclusive and does not include the last position. If the coordinate system is 1-base, then end is inclusive and includes the last position.
   */
  end?: number;
  /**
   * Harmonic mean of Recall and Precision, computed as: 2 * precision * recall / (precision + recall).
   */
  fScore?: number;
  /**
   * The number of false positives where the non-REF alleles in the Truth and Query Call Sets match (i.e. cases where the truth is 1/1 and the query is 0/1 or similar).
   */
  gtFP?: number;
  /**
   * Which method is used to get sequence quality.
   */
  method?: CodeableConcept;
  /**
   * QUERY.TP / (QUERY.TP + QUERY.FP).
   */
  precision?: number;
  /**
   * False positives, i.e. the number of sites in the Query Call Set for which there is no path through the Truth Call Set that is consistent with this site. Sites with correct variant but incorrect genotype are counted here.
   */
  queryFP?: number;
  /**
   * True positives, from the perspective of the query data, i.e. the number of sites in the Query Call Set for which there are paths through the Truth Call Set that are consistent with all of the alleles at this site, and for which there is an accurate genotype call for the event.
   */
  queryTP?: number;
  /**
   * TRUTH.TP / (TRUTH.TP + TRUTH.FN).
   */
  recall?: number;
  /**
   * Receiver Operator Characteristic (ROC) Curve  to give sensitivity/specificity tradeoff.
   */
  roc?: MolecularSequenceQualityRoc;
  /**
   * The score of an experimentally derived feature such as a p-value ([SO:0001685](http://www.sequenceontology.org/browser/current_svn/term/SO:0001685)).
   */
  score?: Quantity;
  /**
   * Gold standard sequence used for comparing against.
   */
  standardSequence?: CodeableConcept;
  /**
   * Start position of the sequence. If the coordinate system is either 0-based or 1-based, then start position is inclusive.
   */
  start?: number;
  /**
   * False negatives, i.e. the number of sites in the Truth Call Set for which there is no path through the Query Call Set that is consistent with all of the alleles at this site, or sites for which there is an inaccurate genotype call for the event. Sites with correct variant but incorrect genotype are counted here.
   */
  truthFN?: number;
  /**
   * True positives, from the perspective of the truth data, i.e. the number of sites in the Truth Call Set for which there are paths through the Query Call Set that are consistent with all of the alleles at this site, and for which there is an accurate genotype call for the event.
   */
  truthTP?: number;
  /**
   * INDEL / SNP / Undefined variant.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the MolecularSequence.quality.type field
 */
export enum MolecularSequenceQualityTypeCodes {
  INDEL = 'indel',
  SNP = 'snp',
  UNKNOWN = 'unknown',
}
/**
 * Configurations of the external repository. The repository shall store target's observedSeq or records related with target's observedSeq.
 */
export interface MolecularSequenceRepository extends BackboneElement {
  /**
   * Id of the variant in this external repository. The server will understand how to use this id to call for more info about datasets in external repository.
   */
  datasetId?: string;
  _datasetId?: Element;
  /**
   * URI of an external repository which contains further details about the genetics data.
   */
  name?: string;
  _name?: Element;
  /**
   * Id of the read in this external repository.
   */
  readsetId?: string;
  _readsetId?: Element;
  /**
   * Click and see / RESTful API / Need login to see / RESTful API with authentication / Other ways to see resource.
   */
  type: string;
  _type?: Element;
  /**
   * URI of an external repository which contains further details about the genetics data.
   */
  url?: string;
  _url?: Element;
  /**
   * Id of the variantset in this external repository. The server will understand how to use this id to call for more info about variantsets in external repository.
   */
  variantsetId?: string;
  _variantsetId?: Element;
}
/**
 * Code Values for the MolecularSequence.repository.type field
 */
export enum MolecularSequenceRepositoryTypeCodes {
  DIRECTLINK = 'directlink',
  OPENAPI = 'openapi',
  LOGIN = 'login',
  OAUTH = 'oauth',
  OTHER = 'other',
}
/**
 * Structural variant outer.
 */
export interface MolecularSequenceStructureVariantOuter extends BackboneElement {
  /**
   * Structural variant outer end. If the coordinate system is 0-based then end is exclusive and does not include the last position. If the coordinate system is 1-base, then end is inclusive and includes the last position.
   */
  end?: number;
  /**
   * Structural variant outer start. If the coordinate system is either 0-based or 1-based, then start position is inclusive.
   */
  start?: number;
}
/**
 * Structural variant inner.
 */
export interface MolecularSequenceStructureVariantInner extends BackboneElement {
  /**
   * Structural variant inner end. If the coordinate system is 0-based then end is exclusive and does not include the last position. If the coordinate system is 1-base, then end is inclusive and includes the last position.
   */
  end?: number;
  /**
   * Structural variant inner start. If the coordinate system is either 0-based or 1-based, then start position is inclusive.
   */
  start?: number;
}
/**
 * Information about chromosome structure variation.
 */
export interface MolecularSequenceStructureVariant extends BackboneElement {
  /**
   * Used to indicate if the outer and inner start-end values have the same meaning.
   */
  exact?: boolean;
  _exact?: Element;
  /**
   * Structural variant inner.
   */
  inner?: MolecularSequenceStructureVariantInner;
  /**
   * Length of the variant chromosome.
   */
  length?: number;
  /**
   * Structural variant outer.
   */
  outer?: MolecularSequenceStructureVariantOuter;
  /**
   * Information about chromosome structure variation DNA change type.
   */
  variantType?: CodeableConcept;
}
/**
 * Raw data describing a biological sequence.
 */
export interface MolecularSequence extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'MolecularSequence';
  /**
   * Whether the sequence is numbered starting at 0 (0-based numbering or coordinates, inclusive start, exclusive end) or starting at 1 (1-based numbering, inclusive start and inclusive end).
   */
  coordinateSystem: number;
  /**
   * The method for sequencing, for example, chip information.
   */
  device?: Reference;
  /**
   * A unique identifier for this particular sequence instance. This is a FHIR-defined id.
   */
  identifier?: Identifier[];
  /**
   * Sequence that was observed. It is the result marked by referenceSeq along with variant records on referenceSeq. This shall start from referenceSeq.windowStart and end by referenceSeq.windowEnd.
   */
  observedSeq?: string;
  _observedSeq?: Element;
  /**
   * The patient whose sequencing results are described by this resource.
   */
  patient?: Reference;
  /**
   * The organization or lab that should be responsible for this result.
   */
  performer?: Reference;
  /**
   * Pointer to next atomic sequence which at most contains one variant.
   */
  pointer?: Reference[];
  /**
   * An experimental feature attribute that defines the quality of the feature in a quantitative way, such as a phred quality score ([SO:0001686](http://www.sequenceontology.org/browser/current_svn/term/SO:0001686)).
   */
  quality?: MolecularSequenceQuality[];
  /**
   * The number of copies of the sequence of interest. (RNASeq).
   */
  quantity?: Quantity;
  /**
   * Coverage (read depth or depth) is the average number of reads representing a given nucleotide in the reconstructed sequence.
   */
  readCoverage?: number;
  /**
   * A sequence that is used as a reference to describe variants that are present in a sequence analyzed.
   */
  referenceSeq?: MolecularSequenceReferenceSeq;
  /**
   * Configurations of the external repository. The repository shall store target's observedSeq or records related with target's observedSeq.
   */
  repository?: MolecularSequenceRepository[];
  /**
   * Specimen used for sequencing.
   */
  specimen?: Reference;
  /**
   * Information about chromosome structure variation.
   */
  structureVariant?: MolecularSequenceStructureVariant[];
  /**
   * Amino Acid Sequence/ DNA Sequence / RNA Sequence.
   */
  type?: string;
  _type?: Element;
  /**
   * The definition of variant here originates from Sequence ontology ([variant_of](http://www.sequenceontology.org/browser/current_svn/term/variant_of)). This element can represent amino acid or nucleic sequence change(including insertion,deletion,SNP,etc.)  It can represent some complex mutation or segment variation with the assist of CIGAR string.
   */
  variant?: MolecularSequenceVariant[];
}
/**
 * Code Values for the MolecularSequence.type field
 */
export enum MolecularSequenceTypeCodes {
  AA = 'aa',
  DNA = 'dna',
  RNA = 'rna',
}
/**
 * Multiple identifiers may exist, either due to duplicate registration, regional rules, needs of different communication technologies, etc.
 */
export interface NamingSystemUniqueId extends BackboneElement {
  /**
   * e.g. "must be used in Germany" or "was initially published in error with this value".
   */
  comment?: string;
  _comment?: Element;
  /**
   * Within a registry, a given identifier should only be "active" for a single namespace at a time.  (Ideally, an identifier should only ever be associated with a single namespace across all time).
   */
  period?: Period;
  /**
   * Indicates whether this identifier is the "preferred" identifier of this type.
   */
  preferred?: boolean;
  _preferred?: Element;
  /**
   * Different identifier types may be used in different types of communications (OIDs for v3, URIs for FHIR, etc.).  Other includes RUIDs from v3, standard v2 code name strings, etc.
   */
  type: string;
  _type?: Element;
  /**
   * If the value is a URI intended for use as FHIR system identifier, the URI should not contain "\" or "?" or "," since this makes escaping very difficult.
   */
  value: string;
  _value?: Element;
}
/**
 * Code Values for the NamingSystem.uniqueId.type field
 */
export enum NamingSystemUniqueIdTypeCodes {
  OID = 'oid',
  UUID = 'uuid',
  URI = 'uri',
  OTHER = 'other',
}
/**
 * A curated namespace that issues unique symbols within that namespace for the identification of concepts, people, devices, etc.  Represents a "System" used within the Identifier and Coding data types.
 */
export interface NamingSystem extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'NamingSystem';
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the naming system. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the naming system was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the naming system as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the naming system is presumed to be the predominant language in the place the naming system was created).
   */
  description?: string;
  _description?: Element;
  /**
   * It may be possible for the naming system to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * Indicates the purpose for the naming system - what kinds of things does it make unique?
   */
  kind: string;
  _kind?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.The"symbolic name" for an OID would be captured as an extension.
   */
  name: string;
  _name?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the naming system is the organization or individual primarily responsible for the maintenance and upkeep of the naming system. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the naming system. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This is the primary organization.  Responsibility for some aspects of a namespace may be delegated.
   */
  responsible?: string;
  _responsible?: Element;
  /**
   * Allows filtering of naming systems that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * This will most commonly be used for identifier namespaces, but categories could potentially be useful for code systems and authorities as well.
   */
  type?: CodeableConcept;
  /**
   * Multiple identifiers may exist, either due to duplicate registration, regional rules, needs of different communication technologies, etc.
   */
  uniqueId: NamingSystemUniqueId[];
  /**
   * Provides guidance on the use of the namespace, including the handling of formatting characters, use of upper vs. lower case, etc.
   */
  usage?: string;
  _usage?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
}
/**
 * Code Values for the NamingSystem.kind field
 */
export enum NamingSystemKindCodes {
  CODESYSTEM = 'codesystem',
  IDENTIFIER = 'identifier',
  ROOT = 'root',
}
/**
 * Code Values for the NamingSystem.status field
 */
export enum NamingSystemStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Class that defines the quantity and type of nutrient modifications (for example carbohydrate, fiber or sodium) required for the oral diet.
 */
export interface NutritionOrderOralDietNutrient extends BackboneElement {
  /**
   * The quantity of the specified nutrient to include in diet.
   */
  amount?: Quantity;
  /**
   * The nutrient that is being modified such as carbohydrate or sodium.
   */
  modifier?: CodeableConcept;
}
/**
 * Class that describes any texture modifications required for the patient to safely consume various types of solid foods.
 */
export interface NutritionOrderOralDietTexture extends BackboneElement {
  /**
   * Coupled with the `texture.modifier`; could be (All Foods).
   */
  foodType?: CodeableConcept;
  /**
   * Coupled with the foodType (Meat).
   */
  modifier?: CodeableConcept;
}
/**
 * Diet given orally in contrast to enteral (tube) feeding.
 */
export interface NutritionOrderOralDiet extends BackboneElement {
  /**
   * The required consistency (e.g. honey-thick, nectar-thick, thin, thickened.) of liquids or fluids served to the patient.
   */
  fluidConsistencyType?: CodeableConcept[];
  /**
   * Free text dosage instructions can be used for cases where the instructions are too complex to code.
   */
  instruction?: string;
  _instruction?: Element;
  /**
   * Class that defines the quantity and type of nutrient modifications (for example carbohydrate, fiber or sodium) required for the oral diet.
   */
  nutrient?: NutritionOrderOralDietNutrient[];
  /**
   * The time period and frequency at which the diet should be given.  The diet should be given for the combination of all schedules if more than one schedule is present.
   */
  schedule?: Timing[];
  /**
   * Class that describes any texture modifications required for the patient to safely consume various types of solid foods.
   */
  texture?: NutritionOrderOralDietTexture[];
  /**
   * The kind of diet or dietary restriction such as fiber restricted diet or diabetic diet.
   */
  type?: CodeableConcept[];
}
/**
 * Oral nutritional products given in order to add further nutritional value to the patient's diet.
 */
export interface NutritionOrderSupplement extends BackboneElement {
  /**
   * Free text dosage instructions can be used for cases where the instructions are too complex to code.
   */
  instruction?: string;
  _instruction?: Element;
  /**
   * The product or brand name of the nutritional supplement such as "Acme Protein Shake".
   */
  productName?: string;
  _productName?: Element;
  /**
   * The amount of the nutritional supplement to be given.
   */
  quantity?: Quantity;
  /**
   * The time period and frequency at which the supplement(s) should be given.  The supplement should be given for the combination of all schedules if more than one schedule is present.
   */
  schedule?: Timing[];
  /**
   * The kind of nutritional supplement product required such as a high protein or pediatric clear liquid supplement.
   */
  type?: CodeableConcept;
}
/**
 * See implementation notes below for further discussion on how to order continuous vs bolus enteral feeding using this resource.
 */
export interface NutritionOrderEnteralFormulaAdministration extends BackboneElement {
  /**
   * The volume of formula to provide to the patient per the specified administration schedule.
   */
  quantity?: Quantity;
  /**
   * Ratio is used when the quantity value in the denominator is not "1", otherwise use Quantity. For example, the Ratio datatype is used for "200 mL/4 hrs" versus the Quantity datatype for "50 mL/hr".
   */
  rateQuantity?: Quantity;
  /**
   * Ratio is used when the quantity value in the denominator is not "1", otherwise use Quantity. For example, the Ratio datatype is used for "200 mL/4 hrs" versus the Quantity datatype for "50 mL/hr".
   */
  rateRatio?: Ratio;
  /**
   * The time period and frequency at which the enteral formula should be delivered to the patient.
   */
  schedule?: Timing;
}
/**
 * Feeding provided through the gastrointestinal tract via a tube, catheter, or stoma that delivers nutrition distal to the oral cavity.
 */
export interface NutritionOrderEnteralFormula extends BackboneElement {
  /**
   * The product or brand name of the type of modular component to be added to the formula.
   */
  additiveProductName?: string;
  _additiveProductName?: Element;
  /**
   * Indicates the type of modular component such as protein, carbohydrate, fat or fiber to be provided in addition to or mixed with the base formula.
   */
  additiveType?: CodeableConcept;
  /**
   * See implementation notes below for further discussion on how to order continuous vs bolus enteral feeding using this resource.
   */
  administration?: NutritionOrderEnteralFormulaAdministration[];
  /**
   * Free text dosage instructions can be used for cases where the instructions are too complex to code.
   */
  administrationInstruction?: string;
  _administrationInstruction?: Element;
  /**
   * The product or brand name of the enteral or infant formula product such as "ACME Adult Standard Formula".
   */
  baseFormulaProductName?: string;
  _baseFormulaProductName?: Element;
  /**
   * The type of enteral or infant formula such as an adult standard formula with fiber or a soy-based infant formula.
   */
  baseFormulaType?: CodeableConcept;
  /**
   * The amount of energy (calories) that the formula should provide per specified volume, typically per mL or fluid oz.  For example, an infant may require a formula that provides 24 calories per fluid ounce or an adult may require an enteral formula that provides 1.5 calorie/mL.
   */
  caloricDensity?: Quantity;
  /**
   * The maximum total quantity of formula that may be administered to a subject over the period of time, e.g. 1440 mL over 24 hours.
   */
  maxVolumeToDeliver?: Quantity;
  /**
   * The route or physiological path of administration into the patient's gastrointestinal  tract for purposes of providing the formula feeding, e.g. nasogastric tube.
   */
  routeofAdministration?: CodeableConcept;
}
/**
 * A request to supply a diet, formula feeding (enteral) or oral nutritional supplement to a patient/resident.
 */
export interface NutritionOrder extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'NutritionOrder';
  /**
   * Information on a patient's food allergies and intolerances to inform healthcare personnel about the type of foods that the patient shouldn't receive or consume.
   */
  allergyIntolerance?: Reference[];
  /**
   * The date and time that this nutrition order was requested.
   */
  dateTime: string;
  _dateTime?: Element;
  /**
   * An encounter that provides additional information about the healthcare context in which this request is made.
   */
  encounter?: Reference;
  /**
   * Feeding provided through the gastrointestinal tract via a tube, catheter, or stoma that delivers nutrition distal to the oral cavity.
   */
  enteralFormula?: NutritionOrderEnteralFormula;
  /**
   * Information on a patient's food allergies, intolerances and preferences to inform healthcare personnel about the type  of foods that the patient shouldn't receive or consume.
   */
  excludeFoodModifier?: CodeableConcept[];
  /**
   * Information on a patient's food preferences that inform healthcare personnel about the food that the patient should receive or consume.
   */
  foodPreferenceModifier?: CodeableConcept[];
  /**
   * The Identifier.type element can be to indicate filler vs. placer if needed.  This is explained in further detail [here](servicerequest.html#notes).
   */
  identifier?: Identifier[];
  /**
   * The URL pointing to a protocol, guideline, orderset or other definition that is adhered to in whole or in part by this NutritionOrder.
   */
  instantiates?: string[];
  _instantiates?: Element;
  /**
   * Note: This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  instantiatesCanonical?: string[];
  _instantiatesCanonical?: Element;
  /**
   * This might be an HTML page, PDF, etc. or could just be a non-resolvable URI identifier.
   */
  instantiatesUri?: string[];
  _instantiatesUri?: Element;
  /**
   * When resources map to this element, they are free to define as many codes as necessary to cover their space and will map to "proposal, plan or order".  Can have multiple codes that map to one of these.  E.g. "original order", "encoded order", "reflex order" would all map to "order".  Expectation is that the set of codes is mutually exclusive or a strict all-encompassing hierarchy.
   */
  intent: string;
  _intent?: Element;
  /**
   * This element SHALL NOT be used to supply free text instructions for the diet which are represented in the `.oralDiet.instruction`, `supplement.instruction`, or `enteralFormula.administrationInstruction` elements.
   */
  note?: Annotation[];
  /**
   * Diet given orally in contrast to enteral (tube) feeding.
   */
  oralDiet?: NutritionOrderOralDiet;
  /**
   * The practitioner that holds legal responsibility for ordering the diet, nutritional supplement, or formula feedings.
   */
  orderer?: Reference;
  /**
   * The person (patient) who needs the nutrition order for an oral diet, nutritional supplement and/or enteral or formula feeding.
   */
  patient: Reference;
  /**
   * Typically the system placing the order sets the status to "requested". Thereafter, the order is maintained by the receiver that updates the status as the request is handled.  This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * Oral nutritional products given in order to add further nutritional value to the patient's diet.
   */
  supplement?: NutritionOrderSupplement[];
}
/**
 * Code Values for the NutritionOrder.intent field
 */
export enum NutritionOrderIntentCodes {
  PROPOSAL = 'proposal',
  PLAN = 'plan',
  DIRECTIVE = 'directive',
  ORDER = 'order',
  ORIGINAL_ORDER = 'original-order',
  REFLEX_ORDER = 'reflex-order',
  FILLER_ORDER = 'filler-order',
  INSTANCE_ORDER = 'instance-order',
  OPTION = 'option',
}
/**
 * Code Values for the NutritionOrder.status field
 */
export enum NutritionOrderStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  ON_HOLD = 'on-hold',
  REVOKED = 'revoked',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  UNKNOWN = 'unknown',
}
/**
 * Most observations only have one generic reference range. Systems MAY choose to restrict to only supplying the relevant reference range based on knowledge about the patient (e.g., specific to the patient's age, gender, weight and other factors), but this might not be possible or appropriate. Whenever more than one reference range is supplied, the differences between them SHOULD be provided in the reference range and/or age properties.
 */
export interface ObservationReferenceRange extends BackboneElement {
  /**
   * The age at which this reference range is applicable. This is a neonatal age (e.g. number of weeks at term) if the meaning says so.
   */
  age?: Range;
  /**
   * This SHOULD be populated if there is more than one range.  If this element is not present then the normal population is assumed.
   */
  appliesTo?: CodeableConcept[];
  /**
   * The value of the high bound of the reference range.  The high bound of the reference range endpoint is inclusive of the value (e.g.  reference range is >=5 - <=9). If the high bound is omitted,  it is assumed to be meaningless (e.g. reference range is >= 2.3).
   */
  high?: Quantity;
  /**
   * The value of the low bound of the reference range.  The low bound of the reference range endpoint is inclusive of the value (e.g.  reference range is >=5 - <=9). If the low bound is omitted,  it is assumed to be meaningless (e.g. reference range is <=2.3).
   */
  low?: Quantity;
  /**
   * Text based reference range in an observation which may be used when a quantitative range is not appropriate for an observation.  An example would be a reference value of "Negative" or a list or table of "normals".
   */
  text?: string;
  _text?: Element;
  /**
   * This SHOULD be populated if there is more than one range.  If this element is not present then the normal range is assumed.
   */
  type?: CodeableConcept;
}
/**
 * For a discussion on the ways Observations can be assembled in groups together see [Notes](observation.html#notes) below.
 */
export interface ObservationComponent extends BackboneElement {
  /**
   * *All* code-value and  component.code-component.value pairs need to be taken into account to correctly understand the meaning of the observation.
   */
  code: CodeableConcept;
  /**
   * "Null" or exceptional values can be represented two ways in FHIR Observations.  One way is to simply include them in the value set and represent the exceptions in the value.  For example, measurement values for a serology test could be  "detected", "not detected", "inconclusive", or  "test not done".
   * The alternate way is to use the value element for actual observations and use the explicit dataAbsentReason element to record exceptional values.  For example, the dataAbsentReason code "error" could be used when the measurement was not completed.  Because of these options, use-case agreements are required to interpret general observations for exceptional values.
   */
  dataAbsentReason?: CodeableConcept;
  /**
   * Historically used for laboratory results (known as 'abnormal flag' ),  its use extends to other use cases where coded interpretations  are relevant.  Often reported as one or more simple compact codes this element is often placed adjacent to the result value in reports and flow sheets to signal the meaning/normalcy status of the result.
   */
  interpretation?: CodeableConcept[];
  /**
   * Most observations only have one generic reference range. Systems MAY choose to restrict to only supplying the relevant reference range based on knowledge about the patient (e.g., specific to the patient's age, gender, weight and other factors), but this might not be possible or appropriate. Whenever more than one reference range is supplied, the differences between them SHOULD be provided in the reference range and/or age properties.
   */
  referenceRange?: ObservationReferenceRange[];
  /**
   * Used when observation has a set of component observations. An observation may have both a value (e.g. an  Apgar score)  and component observations (the observations from which the Apgar score was derived). If a value is present, the datatype for this element should be determined by Observation.code. A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueQuantity?: Quantity;
  /**
   * Used when observation has a set of component observations. An observation may have both a value (e.g. an  Apgar score)  and component observations (the observations from which the Apgar score was derived). If a value is present, the datatype for this element should be determined by Observation.code. A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueCodeableConcept?: CodeableConcept;
  /**
   * Used when observation has a set of component observations. An observation may have both a value (e.g. an  Apgar score)  and component observations (the observations from which the Apgar score was derived). If a value is present, the datatype for this element should be determined by Observation.code. A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueString?: string;
  _valueString?: Element;
  /**
   * Used when observation has a set of component observations. An observation may have both a value (e.g. an  Apgar score)  and component observations (the observations from which the Apgar score was derived). If a value is present, the datatype for this element should be determined by Observation.code. A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueBoolean?: boolean;
  _valueBoolean?: Element;
  /**
   * Used when observation has a set of component observations. An observation may have both a value (e.g. an  Apgar score)  and component observations (the observations from which the Apgar score was derived). If a value is present, the datatype for this element should be determined by Observation.code. A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueInteger?: number;
  /**
   * Used when observation has a set of component observations. An observation may have both a value (e.g. an  Apgar score)  and component observations (the observations from which the Apgar score was derived). If a value is present, the datatype for this element should be determined by Observation.code. A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueRange?: Range;
  /**
   * Used when observation has a set of component observations. An observation may have both a value (e.g. an  Apgar score)  and component observations (the observations from which the Apgar score was derived). If a value is present, the datatype for this element should be determined by Observation.code. A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueRatio?: Ratio;
  /**
   * Used when observation has a set of component observations. An observation may have both a value (e.g. an  Apgar score)  and component observations (the observations from which the Apgar score was derived). If a value is present, the datatype for this element should be determined by Observation.code. A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueSampledData?: SampledData;
  /**
   * Used when observation has a set of component observations. An observation may have both a value (e.g. an  Apgar score)  and component observations (the observations from which the Apgar score was derived). If a value is present, the datatype for this element should be determined by Observation.code. A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueTime?: string;
  _valueTime?: Element;
  /**
   * Used when observation has a set of component observations. An observation may have both a value (e.g. an  Apgar score)  and component observations (the observations from which the Apgar score was derived). If a value is present, the datatype for this element should be determined by Observation.code. A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueDateTime?: string;
  _valueDateTime?: Element;
  /**
   * Used when observation has a set of component observations. An observation may have both a value (e.g. an  Apgar score)  and component observations (the observations from which the Apgar score was derived). If a value is present, the datatype for this element should be determined by Observation.code. A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valuePeriod?: Period;
}
/**
 * Measurements and simple assertions made about a patient, device or other subject.
 */
export interface Observation extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Observation';
  /**
   * A plan, proposal or order that is fulfilled in whole or in part by this event.  For example, a MedicationRequest may require a patient to have laboratory test performed before  it is dispensed.
   */
  basedOn?: Reference[];
  /**
   * Only used if not implicit in code found in Observation.code.  In many systems, this may be represented as a related observation instead of an inline component.
   * If the use case requires BodySite to be handled as a separate resource (e.g. to identify and track separately) then use the standard extension[ bodySite](extension-bodysite.html).
   */
  bodySite?: CodeableConcept;
  /**
   * In addition to the required category valueset, this element allows various categorization schemes based on the owner’s definition of the category and effectively multiple categories can be used at once.  The level of granularity is defined by the category concepts in the value set.
   */
  category?: CodeableConcept[];
  /**
   * *All* code-value and, if present, component.code-component.value pairs need to be taken into account to correctly understand the meaning of the observation.
   */
  code: CodeableConcept;
  /**
   * For a discussion on the ways Observations can be assembled in groups together see [Notes](observation.html#notes) below.
   */
  component?: ObservationComponent[];
  /**
   * Null or exceptional values can be represented two ways in FHIR Observations.  One way is to simply include them in the value set and represent the exceptions in the value.  For example, measurement values for a serology test could be  "detected", "not detected", "inconclusive", or  "specimen unsatisfactory".
   * The alternate way is to use the value element for actual observations and use the explicit dataAbsentReason element to record exceptional values.  For example, the dataAbsentReason code "error" could be used when the measurement was not completed. Note that an observation may only be reported if there are values to report. For example differential cell counts values may be reported only when > 0.  Because of these options, use-case agreements are required to interpret general observations for null or exceptional values.
   */
  dataAbsentReason?: CodeableConcept;
  /**
   * All the reference choices that are listed in this element can represent clinical observations and other measurements that may be the source for a derived value.  The most common reference will be another Observation.  For a discussion on the ways Observations can assembled in groups together, see [Notes](observation.html#obsgrouping) below.
   */
  derivedFrom?: Reference[];
  /**
   * Note that this is not meant to represent a device involved in the transmission of the result, e.g., a gateway.  Such devices may be documented using the Provenance resource where relevant.
   */
  device?: Reference;
  /**
   * At least a date should be present unless this observation is a historical report.  For recording imprecise or "fuzzy" times (For example, a blood glucose measurement taken "after breakfast") use the [Timing](datatypes.html#timing) datatype which allow the measurement to be tied to regular life events.
   */
  effectiveDateTime?: string;
  _effectiveDateTime?: Element;
  /**
   * At least a date should be present unless this observation is a historical report.  For recording imprecise or "fuzzy" times (For example, a blood glucose measurement taken "after breakfast") use the [Timing](datatypes.html#timing) datatype which allow the measurement to be tied to regular life events.
   */
  effectivePeriod?: Period;
  /**
   * At least a date should be present unless this observation is a historical report.  For recording imprecise or "fuzzy" times (For example, a blood glucose measurement taken "after breakfast") use the [Timing](datatypes.html#timing) datatype which allow the measurement to be tied to regular life events.
   */
  effectiveTiming?: Timing;
  /**
   * At least a date should be present unless this observation is a historical report.  For recording imprecise or "fuzzy" times (For example, a blood glucose measurement taken "after breakfast") use the [Timing](datatypes.html#timing) datatype which allow the measurement to be tied to regular life events.
   */
  effectiveInstant?: string;
  _effectiveInstant?: Element;
  /**
   * This will typically be the encounter the event occurred within, but some events may be initiated prior to or after the official completion of an encounter but still be tied to the context of the encounter (e.g. pre-admission laboratory tests).
   */
  encounter?: Reference;
  /**
   * Typically, an observation is made about the subject - a patient, or group of patients, location, or device - and the distinction between the subject and what is directly measured for an observation is specified in the observation code itself ( e.g., "Blood Glucose") and does not need to be represented separately using this element.  Use `specimen` if a reference to a specimen is required.  If a code is required instead of a resource use either  `bodysite` for bodysites or the standard extension [focusCode](extension-observation-focuscode.html).
   */
  focus?: Reference[];
  /**
   * When using this element, an observation will typically have either a value or a set of related resources, although both may be present in some cases.  For a discussion on the ways Observations can assembled in groups together, see [Notes](observation.html#obsgrouping) below.  Note that a system may calculate results from [QuestionnaireResponse](questionnaireresponse.html)  into a final score and represent the score as an Observation.
   */
  hasMember?: Reference[];
  /**
   * A unique identifier assigned to this observation.
   */
  identifier?: Identifier[];
  /**
   * Historically used for laboratory results (known as 'abnormal flag' ),  its use extends to other use cases where coded interpretations  are relevant.  Often reported as one or more simple compact codes this element is often placed adjacent to the result value in reports and flow sheets to signal the meaning/normalcy status of the result.
   */
  interpretation?: CodeableConcept[];
  /**
   * For Observations that don’t require review and verification, it may be the same as the [`lastUpdated` ](resource-definitions.html#Meta.lastUpdated) time of the resource itself.  For Observations that do require review and verification for certain updates, it might not be the same as the `lastUpdated` time of the resource itself due to a non-clinically significant update that doesn’t require the new version to be reviewed and verified again.
   */
  issued?: string;
  _issued?: Element;
  /**
   * Only used if not implicit in code for Observation.code.
   */
  method?: CodeableConcept;
  /**
   * May include general statements about the observation, or statements about significant, unexpected or unreliable results values, or information about its source when relevant to its interpretation.
   */
  note?: Annotation[];
  /**
   * To link an Observation to an Encounter use `encounter`.  See the  [Notes](observation.html#obsgrouping) below for guidance on referencing another Observation.
   */
  partOf?: Reference[];
  /**
   * Who was responsible for asserting the observed value as "true".
   */
  performer?: Reference[];
  /**
   * Most observations only have one generic reference range. Systems MAY choose to restrict to only supplying the relevant reference range based on knowledge about the patient (e.g., specific to the patient's age, gender, weight and other factors), but this might not be possible or appropriate. Whenever more than one reference range is supplied, the differences between them SHOULD be provided in the reference range and/or age properties.
   */
  referenceRange?: ObservationReferenceRange[];
  /**
   * Should only be used if not implicit in code found in `Observation.code`.  Observations are not made on specimens themselves; they are made on a subject, but in many cases by the means of a specimen. Note that although specimens are often involved, they are not always tracked and reported explicitly. Also note that observation resources may be used in contexts that track the specimen explicitly (e.g. Diagnostic Report).
   */
  specimen?: Reference;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * One would expect this element to be a cardinality of 1..1. The only circumstance in which the subject can be missing is when the observation is made by a device that does not know the patient. In this case, the observation SHALL be matched to a patient through some context/channel matching technique, and at this point, the observation should be updated.
   */
  subject?: Reference;
  /**
   * An observation may have; 1)  a single value here, 2)  both a value and a set of related or component values,  or 3)  only a set of related or component values. If a value is present, the datatype for this element should be determined by Observation.code.  A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueQuantity?: Quantity;
  /**
   * An observation may have; 1)  a single value here, 2)  both a value and a set of related or component values,  or 3)  only a set of related or component values. If a value is present, the datatype for this element should be determined by Observation.code.  A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueCodeableConcept?: CodeableConcept;
  /**
   * An observation may have; 1)  a single value here, 2)  both a value and a set of related or component values,  or 3)  only a set of related or component values. If a value is present, the datatype for this element should be determined by Observation.code.  A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueString?: string;
  _valueString?: Element;
  /**
   * An observation may have; 1)  a single value here, 2)  both a value and a set of related or component values,  or 3)  only a set of related or component values. If a value is present, the datatype for this element should be determined by Observation.code.  A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueBoolean?: boolean;
  _valueBoolean?: Element;
  /**
   * An observation may have; 1)  a single value here, 2)  both a value and a set of related or component values,  or 3)  only a set of related or component values. If a value is present, the datatype for this element should be determined by Observation.code.  A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueInteger?: number;
  /**
   * An observation may have; 1)  a single value here, 2)  both a value and a set of related or component values,  or 3)  only a set of related or component values. If a value is present, the datatype for this element should be determined by Observation.code.  A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueRange?: Range;
  /**
   * An observation may have; 1)  a single value here, 2)  both a value and a set of related or component values,  or 3)  only a set of related or component values. If a value is present, the datatype for this element should be determined by Observation.code.  A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueRatio?: Ratio;
  /**
   * An observation may have; 1)  a single value here, 2)  both a value and a set of related or component values,  or 3)  only a set of related or component values. If a value is present, the datatype for this element should be determined by Observation.code.  A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueSampledData?: SampledData;
  /**
   * An observation may have; 1)  a single value here, 2)  both a value and a set of related or component values,  or 3)  only a set of related or component values. If a value is present, the datatype for this element should be determined by Observation.code.  A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueTime?: string;
  _valueTime?: Element;
  /**
   * An observation may have; 1)  a single value here, 2)  both a value and a set of related or component values,  or 3)  only a set of related or component values. If a value is present, the datatype for this element should be determined by Observation.code.  A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valueDateTime?: string;
  _valueDateTime?: Element;
  /**
   * An observation may have; 1)  a single value here, 2)  both a value and a set of related or component values,  or 3)  only a set of related or component values. If a value is present, the datatype for this element should be determined by Observation.code.  A CodeableConcept with just a text would be used instead of a string if the field was usually coded, or if the type associated with the Observation.code defines a coded value.  For additional guidance, see the [Notes section](observation.html#notes) below.
   */
  valuePeriod?: Period;
}
/**
 * Code Values for the Observation.status field
 */
export enum ObservationStatusCodes {
  REGISTERED = 'registered',
  PRELIMINARY = 'preliminary',
  FINAL = 'final',
  AMENDED = 'amended',
}
/**
 * Characteristics for quantitative results of this observation.
 */
export interface ObservationDefinitionQuantitativeDetails extends BackboneElement {
  /**
   * Factor for converting value expressed with SI unit to value expressed with customary unit.
   */
  conversionFactor?: number;
  /**
   * Customary unit used to report quantitative results of observations conforming to this ObservationDefinition.
   */
  customaryUnit?: CodeableConcept;
  /**
   * Number of digits after decimal separator when the results of such observations are of type Quantity.
   */
  decimalPrecision?: number;
  /**
   * SI unit used to report quantitative results of observations conforming to this ObservationDefinition.
   */
  unit?: CodeableConcept;
}
/**
 * Multiple  ranges of results qualified by different contexts for ordinal or continuous observations conforming to this ObservationDefinition.
 */
export interface ObservationDefinitionQualifiedInterval extends BackboneElement {
  /**
   * Some analytes vary greatly over age.
   */
  age?: Range;
  /**
   * If this element is not present then the global population is assumed.
   */
  appliesTo?: CodeableConcept[];
  /**
   * The category of interval of values for continuous or ordinal observations conforming to this ObservationDefinition.
   */
  category?: string;
  _category?: Element;
  /**
   * Text based condition for which the reference range is valid.
   */
  condition?: string;
  _condition?: Element;
  /**
   * Codes to indicate the health context the range applies to. For example, the normal or therapeutic range.
   */
  context?: CodeableConcept;
  /**
   * Sex of the population the range applies to.
   */
  gender?: string;
  _gender?: Element;
  /**
   * The gestational age to which this reference range is applicable, in the context of pregnancy.
   */
  gestationalAge?: Range;
  /**
   * The low and high values determining the interval. There may be only one of the two.
   */
  range?: Range;
}
/**
 * Code Values for the ObservationDefinition.qualifiedInterval.category field
 */
export enum ObservationDefinitionQualifiedIntervalCategoryCodes {
  REFERENCE = 'reference',
  CRITICAL = 'critical',
  ABSOLUTE = 'absolute',
}
/**
 * Code Values for the ObservationDefinition.qualifiedInterval.gender field
 */
export enum ObservationDefinitionQualifiedIntervalGenderCodes {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
  UNKNOWN = 'unknown',
}
/**
 * Set of definitional characteristics for a kind of observation or measurement produced or consumed by an orderable health care service.
 */
export interface ObservationDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ObservationDefinition';
  /**
   * The set of abnormal coded results for the observation conforming to this ObservationDefinition.
   */
  abnormalCodedValueSet?: Reference;
  /**
   * This element allows various categorization schemes based on the owner’s definition of the category and effectively multiple categories can be used for one instance of ObservationDefinition. The level of granularity is defined by the category concepts in the value set.
   */
  category?: CodeableConcept[];
  /**
   * Describes what will be observed. Sometimes this is called the observation "name".
   */
  code: CodeableConcept;
  /**
   * The set of critical coded results for the observation conforming to this ObservationDefinition.
   */
  criticalCodedValueSet?: Reference;
  /**
   * A unique identifier assigned to this ObservationDefinition artifact.
   */
  identifier?: Identifier[];
  /**
   * Only used if not implicit in observation code.
   */
  method?: CodeableConcept;
  /**
   * An example of observation allowing multiple results is "bacteria identified by culture". Conversely, the measurement of a potassium level allows a single result.
   */
  multipleResultsAllowed?: boolean;
  _multipleResultsAllowed?: Element;
  /**
   * The set of normal coded results for the observations conforming to this ObservationDefinition.
   */
  normalCodedValueSet?: Reference;
  /**
   * The data types allowed for the value element of the instance observations conforming to this ObservationDefinition.
   */
  permittedDataType?: string[];
  _permittedDataType?: Element;
  /**
   * The preferred name to be used when reporting the results of observations conforming to this ObservationDefinition.
   */
  preferredReportName?: string;
  _preferredReportName?: Element;
  /**
   * Multiple  ranges of results qualified by different contexts for ordinal or continuous observations conforming to this ObservationDefinition.
   */
  qualifiedInterval?: ObservationDefinitionQualifiedInterval[];
  /**
   * Characteristics for quantitative results of this observation.
   */
  quantitativeDetails?: ObservationDefinitionQuantitativeDetails;
  /**
   * The set of valid coded results for the observations  conforming to this ObservationDefinition.
   */
  validCodedValueSet?: Reference;
}
/**
 * Code Values for the ObservationDefinition.permittedDataType field
 */
export enum ObservationDefinitionPermittedDataTypeCodes {
  QUANTITY = 'Quantity',
  CODEABLECONCEPT = 'CodeableConcept',
  STRING = 'string',
  BOOLEAN = 'boolean',
  INTEGER = 'integer',
  RANGE = 'Range',
  RATIO = 'Ratio',
  SAMPLEDDATA = 'SampledData',
  TIME = 'time',
  DATETIME = 'dateTime',
  PERIOD = 'Period',
}
/**
 * Binds to a value set if this parameter is coded (code, Coding, CodeableConcept).
 */
export interface OperationDefinitionParameterBinding extends BackboneElement {
  /**
   * For further discussion, see [Using Terminologies](terminologies.html).
   */
  strength: string;
  _strength?: Element;
  /**
   * For value sets with a referenceResource, the display can contain the value set description.  The reference may be version-specific or not.
   */
  valueSet: string;
  _valueSet?: Element;
}
/**
 * Code Values for the OperationDefinition.parameter.binding.strength field
 */
export enum OperationDefinitionParameterBindingStrengthCodes {
  REQUIRED = 'required',
  EXTENSIBLE = 'extensible',
  PREFERRED = 'preferred',
  EXAMPLE = 'example',
}
/**
 * Resolution applies if the referenced parameter exists.
 */
export interface OperationDefinitionParameterReferencedFrom extends BackboneElement {
  /**
   * The name of the parameter or dot-separated path of parameter names pointing to the resource parameter that is expected to contain a reference to this resource.
   */
  source: string;
  _source?: Element;
  /**
   * The id of the element in the referencing resource that is expected to resolve to this resource.
   */
  sourceId?: string;
  _sourceId?: Element;
}
/**
 * Query Definitions only have one output parameter, named "result". This might not be described, but can be to allow a profile to be defined.
 */
export interface OperationDefinitionParameter extends BackboneElement {
  /**
   * Binds to a value set if this parameter is coded (code, Coding, CodeableConcept).
   */
  binding?: OperationDefinitionParameterBinding;
  /**
   * Describes the meaning or use of this parameter.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * The maximum number of times this element is permitted to appear in the request or response.
   */
  max: string;
  _max?: Element;
  /**
   * The minimum number of times this parameter SHALL appear in the request or response.
   */
  min: number;
  /**
   * This name must be a token (start with a letter in a..z, and only contain letters, numerals, and underscore. Note that for search parameters (type = string, with a search type), the name may be altered by the search modifiers.
   */
  name: string;
  _name?: Element;
  /**
   * Query Definitions only have one output parameter, named "result". This might not be described, but can be to allow a profile to be defined.
   */
  part?: OperationDefinitionParameter[];
  /**
   * Resolution applies if the referenced parameter exists.
   */
  referencedFrom?: OperationDefinitionParameterReferencedFrom[];
  /**
   * How the parameter is understood as a search parameter. This is only used if the parameter type is 'string'.
   */
  searchType?: string;
  _searchType?: Element;
  /**
   * Often, these profiles are the base definitions from the spec (e.g. http://hl7.org/fhir/StructureDefinition/Patient).
   */
  targetProfile?: string[];
  _targetProfile?: Element;
  /**
   * if there is no stated parameter, then the parameter is a multi-part parameter; type and must have at least one part defined.
   */
  type?: string;
  _type?: Element;
  /**
   * If a parameter name is used for both an input and an output parameter, the parameter should be defined twice.
   */
  use: string;
  _use?: Element;
}
/**
 * Code Values for the OperationDefinition.parameter.searchType field
 */
export enum OperationDefinitionParameterSearchTypeCodes {
  NUMBER = 'number',
  DATE = 'date',
  STRING = 'string',
  TOKEN = 'token',
  REFERENCE = 'reference',
  COMPOSITE = 'composite',
  QUANTITY = 'quantity',
  URI = 'uri',
  SPECIAL = 'special',
}
/**
 * Code Values for the OperationDefinition.parameter.use field
 */
export enum OperationDefinitionParameterUseCodes {
  IN = 'in',
  OUT = 'out',
}
/**
 * The combinations are suggestions as to which sets of parameters to use together, but the combinations are not intended to be authoritative.
 */
export interface OperationDefinitionOverload extends BackboneElement {
  /**
   * Comments to go on overload.
   */
  comment?: string;
  _comment?: Element;
  /**
   * Name of parameter to include in overload.
   */
  parameterName?: string[];
  _parameterName?: Element;
}
/**
 * A formal computable definition of an operation (on the RESTful interface) or a named query (using the search interaction).
 */
export interface OperationDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'OperationDefinition';
  /**
   * What http methods can be used for the operation depends on the .affectsState value and whether the input parameters are primitive or complex:
   * 1. Servers SHALL support POST method for all operations.
   * 2. Servers SHALL support GET method if all the parameters for the operation are primitive or there are no parameters and the operation has affectsState = false.
   */
  affectsState?: boolean;
  _affectsState?: Element;
  /**
   * A constrained profile can make optional parameters required or not used and clarify documentation.
   */
  base?: string;
  _base?: Element;
  /**
   * The name used to invoke the operation.
   */
  code: string;
  _code?: Element;
  /**
   * Additional information about how to use this operation or named query.
   */
  comment?: string;
  _comment?: Element;
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the operation definition. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the operation definition was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the operation definition as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the operation definition is presumed to be the predominant language in the place the operation definition was created).
   */
  description?: string;
  _description?: Element;
  /**
   * Allows filtering of operation definitions that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * If present the profile shall not conflict with what is specified in the parameters in the operation definition (max/min etc.), though it may provide additional constraints. The constraints expressed in the profile apply whether the operation is invoked by a POST wih parameters or not.
   */
  inputProfile?: string;
  _inputProfile?: Element;
  /**
   * Indicates whether this operation can be invoked on a particular instance of one of the given types.
   */
  instance: boolean;
  _instance?: Element;
  /**
   * It may be possible for the operation definition to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * Named queries are invoked differently, and have different capabilities.
   */
  kind: string;
  _kind?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name: string;
  _name?: Element;
  /**
   * If present the profile shall not conflict with what is specified in the parameters in the operation definition (max/min etc.), though it may provide additional constraints. The constraints expressed in the profile apply whether the operation is invoked by a POST wih parameters or not.
   */
  outputProfile?: string;
  _outputProfile?: Element;
  /**
   * The combinations are suggestions as to which sets of parameters to use together, but the combinations are not intended to be authoritative.
   */
  overload?: OperationDefinitionOverload[];
  /**
   * Query Definitions only have one output parameter, named "result". This might not be described, but can be to allow a profile to be defined.
   */
  parameter?: OperationDefinitionParameter[];
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the operation definition is the organization or individual primarily responsible for the maintenance and upkeep of the operation definition. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the operation definition. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the operation definition. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this operation definition.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * If the type is an abstract resource ("Resource" or "DomainResource") then the operation can be invoked on any concrete specialization.
   */
  resource?: string[];
  _resource?: Element;
  /**
   * Allows filtering of operation definitions that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * Indicates whether this operation or named query can be invoked at the system level (e.g. without needing to choose a resource type for the context).
   */
  system: boolean;
  _system?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Indicates whether this operation or named query can be invoked at the resource type level for any given resource type level (e.g. without needing to choose a specific resource id for the context).
   */
  type: boolean;
  _type?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different operation definition instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the operation definition with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the OperationDefinition.kind field
 */
export enum OperationDefinitionKindCodes {
  OPERATION = 'operation',
  QUERY = 'query',
}
/**
 * Code Values for the OperationDefinition.status field
 */
export enum OperationDefinitionStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * An error, warning, or information message that results from a system action.
 */
export interface OperationOutcomeIssue extends BackboneElement {
  /**
   * Describes the type of the issue. The system that creates an OperationOutcome SHALL choose the most applicable code from the IssueType value set, and may additional provide its own code for the error in the details element.
   */
  code: string;
  _code?: Element;
  /**
   * A human readable description of the error issue SHOULD be placed in details.text.
   */
  details?: CodeableConcept;
  /**
   * This may be a description of how a value is erroneous, a stack dump to help trace the issue or other troubleshooting information.
   */
  diagnostics?: string;
  _diagnostics?: Element;
  /**
   * The root of the FHIRPath is the resource or bundle that generated OperationOutcome.  Each FHIRPath SHALL resolve to a single node.
   */
  expression?: string[];
  _expression?: Element;
  /**
   * The root of the XPath is the resource or bundle that generated OperationOutcome.  Each XPath SHALL resolve to a single node.  This element is deprecated, and is being replaced by expression.
   */
  location?: string[];
  _location?: Element;
  /**
   * This is labeled as "Is Modifier" because applications should not confuse hints and warnings with errors.
   */
  severity: string;
  _severity?: Element;
}
/**
 * Code Values for the OperationOutcome.issue.severity field
 */
export enum OperationOutcomeIssueSeverityCodes {
  FATAL = 'fatal',
  ERROR = 'error',
  WARNING = 'warning',
  INFORMATION = 'information',
}
/**
 * A collection of error, warning, or information messages that result from a system action.
 */
export interface OperationOutcome extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'OperationOutcome';
  /**
   * An error, warning, or information message that results from a system action.
   */
  issue: OperationOutcomeIssue[];
}
/**
 * Where multiple contacts for the same purpose are provided there is a standard extension that can be used to determine which one is the preferred contact to use.
 */
export interface OrganizationContact extends BackboneElement {
  /**
   * Visiting or postal addresses for the contact.
   */
  address?: Address;
  /**
   * A name associated with the contact.
   */
  name?: HumanName;
  /**
   * Indicates a purpose for which the contact can be reached.
   */
  purpose?: CodeableConcept;
  /**
   * A contact detail (e.g. a telephone number or an email address) by which the party may be contacted.
   */
  telecom?: ContactPoint[];
}
/**
 * A formally or informally recognized grouping of people or organizations formed for the purpose of achieving some form of collective action.  Includes companies, institutions, corporations, departments, community groups, healthcare practice groups, payer/insurer, etc.
 */
export interface Organization extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Organization';
  /**
   * This active flag is not intended to be used to mark an organization as temporarily closed or under construction. Instead the Location(s) within the Organization should have the suspended status. If further details of the reason for the suspension are required, then an extension on this element should be used.
   * This element is labeled as a modifier because it may be used to mark that the resource was created in error.
   */
  active?: boolean;
  _active?: Element;
  /**
   * Organization may have multiple addresses with different uses or applicable periods. The use code 'home' is not to be used.
   */
  address?: Address[];
  /**
   * There are no dates associated with the alias/historic names, as this is not intended to track when names were used, but to assist in searching so that older names can still result in identifying the organization.
   */
  alias?: string[];
  _alias?: Element;
  /**
   * Where multiple contacts for the same purpose are provided there is a standard extension that can be used to determine which one is the preferred contact to use.
   */
  contact?: OrganizationContact[];
  /**
   * Technical endpoints providing access to services operated for the organization.
   */
  endpoint?: Reference[];
  /**
   * Identifier for the organization that is used to identify the organization across multiple disparate systems.
   */
  identifier?: Identifier[];
  /**
   * If the name of an organization changes, consider putting the old name in the alias column so that it can still be located through searches.
   */
  name?: string;
  _name?: Element;
  /**
   * The organization of which this organization forms a part.
   */
  partOf?: Reference;
  /**
   * The use code 'home' is not to be used. Note that these contacts are not the contact details of people who are employed by or represent the organization, but official contacts for the organization itself.
   */
  telecom?: ContactPoint[];
  /**
   * Organizations can be corporations, wards, sections, clinical teams, government departments, etc. Note that code is generally a classifier of the type of organization; in many applications, codes are used to identity a particular organization (say, ward) as opposed to another of the same type - these are identifiers, not codes
   * When considering if multiple types are appropriate, you should evaluate if child organizations would be a more appropriate use of the concept, as different types likely are in different sub-areas of the organization. This is most likely to be used where type values have orthogonal values, such as a religious, academic and medical center.
   * We expect that some jurisdictions will profile this optionality to be a single cardinality.
   */
  type?: CodeableConcept[];
}
/**
 * Defines an affiliation/assotiation/relationship between 2 distinct oganizations, that is not a part-of relationship/sub-division relationship.
 */
export interface OrganizationAffiliation extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'OrganizationAffiliation';
  /**
   * If this value is false, you may refer to the period to see when the role was in active use. If there is no period specified, no inference can be made about when it was active.
   */
  active?: boolean;
  _active?: Element;
  /**
   * Definition of the role the participatingOrganization plays in the association.
   */
  code?: CodeableConcept[];
  /**
   * Technical endpoints providing access to services operated for this role.
   */
  endpoint?: Reference[];
  /**
   * Healthcare services provided through the role.
   */
  healthcareService?: Reference[];
  /**
   * Business identifiers that are specific to this role.
   */
  identifier?: Identifier[];
  /**
   * The location(s) at which the role occurs.
   */
  location?: Reference[];
  /**
   * Health insurance provider network in which the participatingOrganization provides the role's services (if defined) at the indicated locations (if defined).
   */
  network?: Reference[];
  /**
   * Organization where the role is available (primary organization/has members).
   */
  organization?: Reference;
  /**
   * The Participating Organization provides/performs the role(s) defined by the code to the Primary Organization (e.g. providing services or is a member of).
   */
  participatingOrganization?: Reference;
  /**
   * The period during which the participatingOrganization is affiliated with the primary organization.
   */
  period?: Period;
  /**
   * Specific specialty of the participatingOrganization in the context of the role.
   */
  specialty?: CodeableConcept[];
  /**
   * Contact details at the participatingOrganization relevant to this Affiliation.
   */
  telecom?: ContactPoint[];
}
/**
 * A parameter passed to or received from the operation.
 */
export interface ParametersParameter extends BackboneElement {
  /**
   * The name of the parameter (reference to the operation definition).
   */
  name: string;
  _name?: Element;
  /**
   * Only one level of nested parameters is allowed.
   */
  part?: ParametersParameter[];
  /**
   * When resolving references in resources, the operation definition may specify how references may be resolved between parameters. If a reference cannot be resolved between the parameters, the application should fall back to it's general resource resolution methods.
   */
  resource?: Resource;
  /**
   * If the parameter is a data type.
   */
  valueBase64Binary?: string;
  _valueBase64Binary?: Element;
  /**
   * If the parameter is a data type.
   */
  valueBoolean?: boolean;
  _valueBoolean?: Element;
  /**
   * If the parameter is a data type.
   */
  valueCanonical?: string;
  _valueCanonical?: Element;
  /**
   * If the parameter is a data type.
   */
  valueCode?: string;
  _valueCode?: Element;
  /**
   * If the parameter is a data type.
   */
  valueDate?: string;
  _valueDate?: Element;
  /**
   * If the parameter is a data type.
   */
  valueDateTime?: string;
  _valueDateTime?: Element;
  /**
   * If the parameter is a data type.
   */
  valueDecimal?: number;
  /**
   * If the parameter is a data type.
   */
  valueId?: string;
  _valueId?: Element;
  /**
   * If the parameter is a data type.
   */
  valueInstant?: string;
  _valueInstant?: Element;
  /**
   * If the parameter is a data type.
   */
  valueInteger?: number;
  /**
   * If the parameter is a data type.
   */
  valueMarkdown?: string;
  _valueMarkdown?: Element;
  /**
   * If the parameter is a data type.
   */
  valueOid?: string;
  _valueOid?: Element;
  /**
   * If the parameter is a data type.
   */
  valuePositiveInt?: number;
  /**
   * If the parameter is a data type.
   */
  valueString?: string;
  _valueString?: Element;
  /**
   * If the parameter is a data type.
   */
  valueTime?: string;
  _valueTime?: Element;
  /**
   * If the parameter is a data type.
   */
  valueUnsignedInt?: number;
  /**
   * If the parameter is a data type.
   */
  valueUri?: string;
  _valueUri?: Element;
  /**
   * If the parameter is a data type.
   */
  valueUrl?: string;
  _valueUrl?: Element;
  /**
   * If the parameter is a data type.
   */
  valueUuid?: string;
  _valueUuid?: Element;
  /**
   * If the parameter is a data type.
   */
  valueAddress?: Address;
  /**
   * If the parameter is a data type.
   */
  valueAge?: Age;
  /**
   * If the parameter is a data type.
   */
  valueAnnotation?: Annotation;
  /**
   * If the parameter is a data type.
   */
  valueAttachment?: Attachment;
  /**
   * If the parameter is a data type.
   */
  valueCodeableConcept?: CodeableConcept;
  /**
   * If the parameter is a data type.
   */
  valueCoding?: Coding;
  /**
   * If the parameter is a data type.
   */
  valueContactPoint?: ContactPoint;
  /**
   * If the parameter is a data type.
   */
  valueCount?: Count;
  /**
   * If the parameter is a data type.
   */
  valueDistance?: Distance;
  /**
   * If the parameter is a data type.
   */
  valueDuration?: Duration;
  /**
   * If the parameter is a data type.
   */
  valueHumanName?: HumanName;
  /**
   * If the parameter is a data type.
   */
  valueIdentifier?: Identifier;
  /**
   * If the parameter is a data type.
   */
  valueMoney?: Money;
  /**
   * If the parameter is a data type.
   */
  valuePeriod?: Period;
  /**
   * If the parameter is a data type.
   */
  valueQuantity?: Quantity;
  /**
   * If the parameter is a data type.
   */
  valueRange?: Range;
  /**
   * If the parameter is a data type.
   */
  valueRatio?: Ratio;
  /**
   * If the parameter is a data type.
   */
  valueReference?: Reference;
  /**
   * If the parameter is a data type.
   */
  valueSampledData?: SampledData;
  /**
   * If the parameter is a data type.
   */
  valueSignature?: Signature;
  /**
   * If the parameter is a data type.
   */
  valueTiming?: Timing;
  /**
   * If the parameter is a data type.
   */
  valueContactDetail?: ContactDetail;
  /**
   * If the parameter is a data type.
   */
  valueContributor?: Contributor;
  /**
   * If the parameter is a data type.
   */
  valueDataRequirement?: DataRequirement;
  /**
   * If the parameter is a data type.
   */
  valueExpression?: Expression;
  /**
   * If the parameter is a data type.
   */
  valueParameterDefinition?: ParameterDefinition;
  /**
   * If the parameter is a data type.
   */
  valueRelatedArtifact?: RelatedArtifact;
  /**
   * If the parameter is a data type.
   */
  valueTriggerDefinition?: TriggerDefinition;
  /**
   * If the parameter is a data type.
   */
  valueUsageContext?: UsageContext;
  /**
   * If the parameter is a data type.
   */
  valueDosage?: Dosage;
  /**
   * If the parameter is a data type.
   */
  valueMeta?: Meta;
}
/**
 * This resource is a non-persisted resource used to pass information into and back from an [operation](operations.html). It has no other use, and there is no RESTful endpoint associated with it.
 */
export interface Parameters extends Resource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Parameters';
  /**
   * A parameter passed to or received from the operation.
   */
  parameter?: ParametersParameter[];
}
/**
 * Contact covers all kinds of contact parties: family members, business contacts, guardians, caregivers. Not applicable to register pedigree and family ties beyond use of having contact.
 */
export interface PatientContact extends BackboneElement {
  /**
   * Address for the contact person.
   */
  address?: Address;
  /**
   * Administrative Gender - the gender that the contact person is considered to have for administration and record keeping purposes.
   */
  gender?: string;
  _gender?: Element;
  /**
   * A name associated with the contact person.
   */
  name?: HumanName;
  /**
   * Organization on behalf of which the contact is acting or for which the contact is working.
   */
  organization?: Reference;
  /**
   * The period during which this contact person or organization is valid to be contacted relating to this patient.
   */
  period?: Period;
  /**
   * The nature of the relationship between the patient and the contact person.
   */
  relationship?: CodeableConcept[];
  /**
   * Contact may have multiple ways to be contacted with different uses or applicable periods.  May need to have options for contacting the person urgently, and also to help with identification.
   */
  telecom?: ContactPoint[];
}
/**
 * Code Values for the Patient.contact.gender field
 */
export enum PatientContactGenderCodes {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
  UNKNOWN = 'unknown',
}
/**
 * If no language is specified, this *implies* that the default local language is spoken.  If you need to convey proficiency for multiple modes, then you need multiple Patient.Communication associations.   For animals, language is not a relevant field, and should be absent from the instance. If the Patient does not speak the default local language, then the Interpreter Required Standard can be used to explicitly declare that an interpreter is required.
 */
export interface PatientCommunication extends BackboneElement {
  /**
   * The structure aa-BB with this exact casing is one the most widely used notations for locale. However not all systems actually code this but instead have it as free text. Hence CodeableConcept instead of code as the data type.
   */
  language: CodeableConcept;
  /**
   * This language is specifically identified for communicating healthcare information.
   */
  preferred?: boolean;
  _preferred?: Element;
}
/**
 * There is no assumption that linked patient records have mutual links.
 */
export interface PatientLink extends BackboneElement {
  /**
   * Referencing a RelatedPerson here removes the need to use a Person record to associate a Patient and RelatedPerson as the same individual.
   */
  other: Reference;
  /**
   * The type of link between this patient resource and another patient resource.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the Patient.link.type field
 */
export enum PatientLinkTypeCodes {
  REPLACED_BY = 'replaced-by',
  REPLACES = 'replaces',
  REFER = 'refer',
  SEEALSO = 'seealso',
}
/**
 * Demographics and other administrative information about an individual or animal receiving care or other health-related services.
 */
export interface Patient extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Patient';
  /**
   * If a record is inactive, and linked to an active record, then future patient/record updates should occur on the other patient.
   */
  active?: boolean;
  _active?: Element;
  /**
   * Patient may have multiple addresses with different uses or applicable periods.
   */
  address?: Address[];
  /**
   * At least an estimated year should be provided as a guess if the real DOB is unknown  There is a standard extension "patient-birthTime" available that should be used where Time is required (such as in maternity/infant care systems).
   */
  birthDate?: string;
  _birthDate?: Element;
  /**
   * If no language is specified, this *implies* that the default local language is spoken.  If you need to convey proficiency for multiple modes, then you need multiple Patient.Communication associations.   For animals, language is not a relevant field, and should be absent from the instance. If the Patient does not speak the default local language, then the Interpreter Required Standard can be used to explicitly declare that an interpreter is required.
   */
  communication?: PatientCommunication[];
  /**
   * Contact covers all kinds of contact parties: family members, business contacts, guardians, caregivers. Not applicable to register pedigree and family ties beyond use of having contact.
   */
  contact?: PatientContact[];
  /**
   * If there's no value in the instance, it means there is no statement on whether or not the individual is deceased. Most systems will interpret the absence of a value as a sign of the person being alive.
   */
  deceasedBoolean?: boolean;
  _deceasedBoolean?: Element;
  /**
   * If there's no value in the instance, it means there is no statement on whether or not the individual is deceased. Most systems will interpret the absence of a value as a sign of the person being alive.
   */
  deceasedDateTime?: string;
  _deceasedDateTime?: Element;
  /**
   * The gender might not match the biological sex as determined by genetics or the individual's preferred identification. Note that for both humans and particularly animals, there are other legitimate possibilities than male and female, though the vast majority of systems and contexts only support male and female.  Systems providing decision support or enforcing business rules should ideally do this on the basis of Observations dealing with the specific sex or gender aspect of interest (anatomical, chromosomal, social, etc.)  However, because these observations are infrequently recorded, defaulting to the administrative gender is common practice.  Where such defaulting occurs, rule enforcement should allow for the variation between administrative and biological, chromosomal and other gender aspects.  For example, an alert about a hysterectomy on a male should be handled as a warning or overridable error, not a "hard" error.  See the Patient Gender and Sex section for additional information about communicating patient gender and sex.
   */
  gender?: string;
  _gender?: Element;
  /**
   * This may be the primary care provider (in a GP context), or it may be a patient nominated care manager in a community/disability setting, or even organization that will provide people to perform the care provider roles.  It is not to be used to record Care Teams, these should be in a CareTeam resource that may be linked to the CarePlan or EpisodeOfCare resources.
   * Multiple GPs may be recorded against the patient for various reasons, such as a student that has his home GP listed along with the GP at university during the school semesters, or a "fly-in/fly-out" worker that has the onsite GP also included with his home GP to remain aware of medical issues.
   * Jurisdictions may decide that they can profile this down to 1 if desired, or 1 per type.
   */
  generalPractitioner?: Reference[];
  /**
   * An identifier for this patient.
   */
  identifier?: Identifier[];
  /**
   * There is no assumption that linked patient records have mutual links.
   */
  link?: PatientLink[];
  /**
   * There is only one managing organization for a specific patient record. Other organizations will have their own Patient record, and may use the Link property to join the records together (or a Person resource which can include confidence ratings for the association).
   */
  managingOrganization?: Reference;
  /**
   * This field contains a patient's most recent marital (civil) status.
   */
  maritalStatus?: CodeableConcept;
  /**
   * Where the valueInteger is provided, the number is the birth number in the sequence. E.g. The middle birth in triplets would be valueInteger=2 and the third born would have valueInteger=3 If a boolean value was provided for this triplets example, then all 3 patient records would have valueBoolean=true (the ordering is not indicated).
   */
  multipleBirthBoolean?: boolean;
  _multipleBirthBoolean?: Element;
  /**
   * Where the valueInteger is provided, the number is the birth number in the sequence. E.g. The middle birth in triplets would be valueInteger=2 and the third born would have valueInteger=3 If a boolean value was provided for this triplets example, then all 3 patient records would have valueBoolean=true (the ordering is not indicated).
   */
  multipleBirthInteger?: number;
  /**
   * A patient may have multiple names with different uses or applicable periods. For animals, the name is a "HumanName" in the sense that is assigned and used by humans and has the same patterns.
   */
  name?: HumanName[];
  /**
   * Guidelines:
   * * Use id photos, not clinical photos.
   * * Limit dimensions to thumbnail.
   * * Keep byte count low to ease resource updates.
   */
  photo?: Attachment[];
  /**
   * A Patient may have multiple ways to be contacted with different uses or applicable periods.  May need to have options for contacting the person urgently and also to help with identification. The address might not go directly to the individual, but may reach another party that is able to proxy for the patient (i.e. home phone, or pet owner's phone).
   */
  telecom?: ContactPoint[];
}
/**
 * Code Values for the Patient.gender field
 */
export enum PatientGenderCodes {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
  UNKNOWN = 'unknown',
}
/**
 * This resource provides the status of the payment for goods and services rendered, and the request and response resource references.
 */
export interface PaymentNotice extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'PaymentNotice';
  /**
   * The amount sent to the payee.
   */
  amount: Money;
  /**
   * The date when this resource was created.
   */
  created: string;
  _created?: Element;
  /**
   * A unique identifier assigned to this payment notice.
   */
  identifier?: Identifier[];
  /**
   * The party who will receive or has received payment that is the subject of this notification.
   */
  payee?: Reference;
  /**
   * A reference to the payment which is the subject of this notice.
   */
  payment: Reference;
  /**
   * The date when the above payment action occurred.
   */
  paymentDate?: string;
  _paymentDate?: Element;
  /**
   * Typically paid: payment sent, cleared: payment received.
   */
  paymentStatus?: CodeableConcept;
  /**
   * The practitioner who is responsible for the services rendered to the patient.
   */
  provider?: Reference;
  /**
   * The party who is notified of the payment status.
   */
  recipient: Reference;
  /**
   * Reference of resource for which payment is being made.
   */
  request?: Reference;
  /**
   * Reference of response to resource for which payment is being made.
   */
  response?: Reference;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
}
/**
 * Code Values for the PaymentNotice.status field
 */
export enum PaymentNoticeStatusCodes {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  DRAFT = 'draft',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Distribution of the payment amount for a previously acknowledged payable.
 */
export interface PaymentReconciliationDetail extends BackboneElement {
  /**
   * The monetary amount allocated from the total payment to the payable.
   */
  amount?: Money;
  /**
   * The date from the response resource containing a commitment to pay.
   */
  date?: string;
  _date?: Element;
  /**
   * Unique identifier for the current payment item for the referenced payable.
   */
  identifier?: Identifier;
  /**
   * The party which is receiving the payment.
   */
  payee?: Reference;
  /**
   * Unique identifier for the prior payment item for the referenced payable.
   */
  predecessor?: Identifier;
  /**
   * A resource, such as a Claim, the evaluation of which could lead to payment.
   */
  request?: Reference;
  /**
   * A resource, such as a ClaimResponse, which contains a commitment to payment.
   */
  response?: Reference;
  /**
   * A reference to the individual who is responsible for inquiries regarding the response and its payment.
   */
  responsible?: Reference;
  /**
   * The party which submitted the claim or financial transaction.
   */
  submitter?: Reference;
  /**
   * For example: payment, adjustment, funds advance, etc.
   */
  type: CodeableConcept;
}
/**
 * A note that describes or explains the processing in a human readable form.
 */
export interface PaymentReconciliationProcessNote extends BackboneElement {
  /**
   * The explanation or description associated with the processing.
   */
  text?: string;
  _text?: Element;
  /**
   * The business purpose of the note text.
   */
  type?: string;
  _type?: Element;
}
/**
 * Code Values for the PaymentReconciliation.processNote.type field
 */
export enum PaymentReconciliationProcessNoteTypeCodes {
  DISPLAY = 'display',
  PRINT = 'print',
  PRINTOPER = 'printoper',
}
/**
 * This resource provides the details including amount of a payment and allocates the payment items being paid.
 */
export interface PaymentReconciliation extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'PaymentReconciliation';
  /**
   * The date when the resource was created.
   */
  created: string;
  _created?: Element;
  /**
   * Distribution of the payment amount for a previously acknowledged payable.
   */
  detail?: PaymentReconciliationDetail[];
  /**
   * A human readable description of the status of the request for the reconciliation.
   */
  disposition?: string;
  _disposition?: Element;
  /**
   * May be needed to identify specific jurisdictional forms.
   */
  formCode?: CodeableConcept;
  /**
   * A unique identifier assigned to this payment reconciliation.
   */
  identifier?: Identifier[];
  /**
   * The resource may be used to indicate that: the request has been held (queued) for processing; that it has been processed and errors found (error); that no errors were found and that some of the adjudication has been undertaken (partial) or that all of the adjudication has been undertaken (complete).
   */
  outcome?: string;
  _outcome?: Element;
  /**
   * Total payment amount as indicated on the financial instrument.
   */
  paymentAmount: Money;
  /**
   * The date of payment as indicated on the financial instrument.
   */
  paymentDate: string;
  _paymentDate?: Element;
  /**
   * For example: EFT number or check number.
   */
  paymentIdentifier?: Identifier;
  /**
   * This party is also responsible for the reconciliation.
   */
  paymentIssuer?: Reference;
  /**
   * The period of time for which payments have been gathered into this bulk payment for settlement.
   */
  period?: Period;
  /**
   * A note that describes or explains the processing in a human readable form.
   */
  processNote?: PaymentReconciliationProcessNote[];
  /**
   * Original request resource reference.
   */
  request?: Reference;
  /**
   * The practitioner who is responsible for the services rendered to the patient.
   */
  requestor?: Reference;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
}
/**
 * Code Values for the PaymentReconciliation.outcome field
 */
export enum PaymentReconciliationOutcomeCodes {
  QUEUED = 'queued',
  COMPLETE = 'complete',
  ERROR = 'error',
  PARTIAL = 'partial',
}
/**
 * Code Values for the PaymentReconciliation.status field
 */
export enum PaymentReconciliationStatusCodes {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  DRAFT = 'draft',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Link to a resource that concerns the same actual person.
 */
export interface PersonLink extends BackboneElement {
  /**
   * Level of assurance that this link is associated with the target resource.
   */
  assurance?: string;
  _assurance?: Element;
  /**
   * The resource to which this actual person is associated.
   */
  target: Reference;
}
/**
 * Code Values for the Person.link.assurance field
 */
export enum PersonLinkAssuranceCodes {
  LEVEL1 = 'level1',
  LEVEL2 = 'level2',
  LEVEL3 = 'level3',
  LEVEL4 = 'level4',
}
/**
 * Demographics and administrative information about a person independent of a specific health-related context.
 */
export interface Person extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Person';
  /**
   * Whether this person's record is in active use.
   */
  active?: boolean;
  _active?: Element;
  /**
   * Person may have multiple addresses with different uses or applicable periods.
   */
  address?: Address[];
  /**
   * At least an estimated year should be provided as a guess if the real DOB is unknown.
   */
  birthDate?: string;
  _birthDate?: Element;
  /**
   * The gender might not match the biological sex as determined by genetics, or the individual's preferred identification. Note that for both humans and particularly animals, there are other legitimate possibilities than M and F, though a clear majority of systems and contexts only support M and F.
   */
  gender?: string;
  _gender?: Element;
  /**
   * Identifier for a person within a particular scope.
   */
  identifier?: Identifier[];
  /**
   * Link to a resource that concerns the same actual person.
   */
  link?: PersonLink[];
  /**
   * The organization that is the custodian of the person record.
   */
  managingOrganization?: Reference;
  /**
   * Person may have multiple names with different uses or applicable periods.
   */
  name?: HumanName[];
  /**
   * An image that can be displayed as a thumbnail of the person to enhance the identification of the individual.
   */
  photo?: Attachment;
  /**
   * Person may have multiple ways to be contacted with different uses or applicable periods.  May need to have options for contacting the person urgently and also to help with identification.
   */
  telecom?: ContactPoint[];
}
/**
 * Code Values for the Person.gender field
 */
export enum PersonGenderCodes {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
  UNKNOWN = 'unknown',
}
/**
 * Indicates what should be done and within what timeframe.
 */
export interface PlanDefinitionGoalTarget extends BackboneElement {
  /**
   * The target value of the measure to be achieved to signify fulfillment of the goal, e.g. 150 pounds or 7.0%. Either the high or low or both values of the range can be specified. When a low value is missing, it indicates that the goal is achieved at any value at or below the high value. Similarly, if the high value is missing, it indicates that the goal is achieved at any value at or above the low value.
   */
  detailQuantity?: Quantity;
  /**
   * The target value of the measure to be achieved to signify fulfillment of the goal, e.g. 150 pounds or 7.0%. Either the high or low or both values of the range can be specified. When a low value is missing, it indicates that the goal is achieved at any value at or below the high value. Similarly, if the high value is missing, it indicates that the goal is achieved at any value at or above the low value.
   */
  detailRange?: Range;
  /**
   * The target value of the measure to be achieved to signify fulfillment of the goal, e.g. 150 pounds or 7.0%. Either the high or low or both values of the range can be specified. When a low value is missing, it indicates that the goal is achieved at any value at or below the high value. Similarly, if the high value is missing, it indicates that the goal is achieved at any value at or above the low value.
   */
  detailCodeableConcept?: CodeableConcept;
  /**
   * Indicates the timeframe after the start of the goal in which the goal should be met.
   */
  due?: Duration;
  /**
   * The parameter whose value is to be tracked, e.g. body weight, blood pressure, or hemoglobin A1c level.
   */
  measure?: CodeableConcept;
}
/**
 * Goals that describe what the activities within the plan are intended to achieve. For example, weight loss, restoring an activity of daily living, obtaining herd immunity via immunization, meeting a process improvement objective, etc.
 */
export interface PlanDefinitionGoal extends BackboneElement {
  /**
   * Identifies problems, conditions, issues, or concerns the goal is intended to address.
   */
  addresses?: CodeableConcept[];
  /**
   * Indicates a category the goal falls within.
   */
  category?: CodeableConcept;
  /**
   * If no code is available, use CodeableConcept.text.
   */
  description: CodeableConcept;
  /**
   * Didactic or other informational resources associated with the goal that provide further supporting information about the goal. Information resources can include inline text commentary and links to web resources.
   */
  documentation?: RelatedArtifact[];
  /**
   * Identifies the expected level of importance associated with reaching/sustaining the defined goal.
   */
  priority?: CodeableConcept;
  /**
   * The event after which the goal should begin being pursued.
   */
  start?: CodeableConcept;
  /**
   * Indicates what should be done and within what timeframe.
   */
  target?: PlanDefinitionGoalTarget[];
}
/**
 * When multiple conditions of the same kind are present, the effects are combined using AND semantics, so the overall condition is true only if all the conditions are true.
 */
export interface PlanDefinitionActionCondition extends BackboneElement {
  /**
   * The expression may be inlined or may be a reference to a named expression within a logic library referenced by the library element.
   */
  expression?: Expression;
  /**
   * Applicability criteria are used to determine immediate applicability when a plan definition is applied to a given context. Start and stop criteria are carried through application and used to describe enter/exit criteria for an action.
   */
  kind: string;
  _kind?: Element;
}
/**
 * Code Values for the PlanDefinition.action.condition.kind field
 */
export enum PlanDefinitionActionConditionKindCodes {
  APPLICABILITY = 'applicability',
  START = 'start',
  STOP = 'stop',
}
/**
 * When an action depends on multiple actions, the meaning is that all actions are dependencies, rather than that any of the actions are a dependency.
 */
export interface PlanDefinitionActionRelatedAction extends BackboneElement {
  /**
   * The element id of the related action.
   */
  actionId: string;
  _actionId?: Element;
  /**
   * A duration or range of durations to apply to the relationship. For example, 30-60 minutes before.
   */
  offsetDuration?: Duration;
  /**
   * A duration or range of durations to apply to the relationship. For example, 30-60 minutes before.
   */
  offsetRange?: Range;
  /**
   * The relationship of this action to the related action.
   */
  relationship: string;
  _relationship?: Element;
}
/**
 * Code Values for the PlanDefinition.action.relatedAction.relationship field
 */
export enum PlanDefinitionActionRelatedActionRelationshipCodes {
  BEFORE_START = 'before-start',
  BEFORE = 'before',
  BEFORE_END = 'before-end',
  CONCURRENT_WITH_START = 'concurrent-with-start',
  CONCURRENT = 'concurrent',
  CONCURRENT_WITH_END = 'concurrent-with-end',
  AFTER_START = 'after-start',
  AFTER = 'after',
  AFTER_END = 'after-end',
}
/**
 * Indicates who should participate in performing the action described.
 */
export interface PlanDefinitionActionParticipant extends BackboneElement {
  /**
   * The role the participant should play in performing the described action.
   */
  role?: CodeableConcept;
  /**
   * The type of participant in the action.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the PlanDefinition.action.participant.type field
 */
export enum PlanDefinitionActionParticipantTypeCodes {
  PATIENT = 'patient',
  PRACTITIONER = 'practitioner',
  RELATED_PERSON = 'related-person',
  DEVICE = 'device',
}
/**
 * Dynamic values are applied in the order in which they are defined in the PlanDefinition resource. Note that when dynamic values are also specified by a referenced ActivityDefinition, the dynamicValues from the ActivityDefinition are applied first, followed by the dynamicValues specified here. In addition, if both a transform and dynamic values are specific, the dynamic values are applied to the result of the transform.
 */
export interface PlanDefinitionActionDynamicValue extends BackboneElement {
  /**
   * The expression may be inlined or may be a reference to a named expression within a logic library referenced by the library element.
   */
  expression?: Expression;
  /**
   * To specify the path to the current action being realized, the %action environment variable is available in this path. For example, to specify the description element of the target action, the path would be %action.description. The path attribute contains a [Simple FHIRPath Subset](fhirpath.html#simple) that allows path traversal, but not calculation.
   */
  path?: string;
  _path?: Element;
}
/**
 * Note that there is overlap between many of the elements defined here and the ActivityDefinition resource. When an ActivityDefinition is referenced (using the definition element), the overlapping elements in the plan override the content of the referenced ActivityDefinition unless otherwise documented in the specific elements. See the PlanDefinition resource for more detailed information.
 */
export interface PlanDefinitionAction extends BackboneElement {
  /**
   * Sub actions that are contained within the action. The behavior of this action determines the functionality of the sub-actions. For example, a selection behavior of at-most-one indicates that of the sub-actions, at most one may be chosen as part of realizing the action definition.
   */
  action?: PlanDefinitionAction[];
  /**
   * Defines whether the action can be selected multiple times.
   */
  cardinalityBehavior?: string;
  _cardinalityBehavior?: Element;
  /**
   * A code that provides meaning for the action or action group. For example, a section may have a LOINC code for the section of a documentation template.
   */
  code?: CodeableConcept[];
  /**
   * When multiple conditions of the same kind are present, the effects are combined using AND semantics, so the overall condition is true only if all the conditions are true.
   */
  condition?: PlanDefinitionActionCondition[];
  /**
   * Note that the definition is optional, and if no definition is specified, a dynamicValue with a root ($this) path can be used to define the entire resource dynamically.
   */
  definitionCanonical?: string;
  _definitionCanonical?: Element;
  /**
   * Note that the definition is optional, and if no definition is specified, a dynamicValue with a root ($this) path can be used to define the entire resource dynamically.
   */
  definitionUri?: string;
  _definitionUri?: Element;
  /**
   * A brief description of the action used to provide a summary to display to the user.
   */
  description?: string;
  _description?: Element;
  /**
   * Didactic or other informational resources associated with the action that can be provided to the CDS recipient. Information resources can include inline text commentary and links to web resources.
   */
  documentation?: RelatedArtifact[];
  /**
   * Dynamic values are applied in the order in which they are defined in the PlanDefinition resource. Note that when dynamic values are also specified by a referenced ActivityDefinition, the dynamicValues from the ActivityDefinition are applied first, followed by the dynamicValues specified here. In addition, if both a transform and dynamic values are specific, the dynamic values are applied to the result of the transform.
   */
  dynamicValue?: PlanDefinitionActionDynamicValue[];
  /**
   * Identifies goals that this action supports. The reference must be to a goal element defined within this plan definition.
   */
  goalId?: string[];
  _goalId?: Element;
  /**
   * Defines the grouping behavior for the action and its children.
   */
  groupingBehavior?: string;
  _groupingBehavior?: Element;
  /**
   * Defines input data requirements for the action.
   */
  input?: DataRequirement[];
  /**
   * Defines the outputs of the action, if any.
   */
  output?: DataRequirement[];
  /**
   * Indicates who should participate in performing the action described.
   */
  participant?: PlanDefinitionActionParticipant[];
  /**
   * Defines whether the action should usually be preselected.
   */
  precheckBehavior?: string;
  _precheckBehavior?: Element;
  /**
   * A user-visible prefix for the action.
   */
  prefix?: string;
  _prefix?: Element;
  /**
   * Indicates how quickly the action should be addressed with respect to other actions.
   */
  priority?: string;
  _priority?: Element;
  /**
   * This is different than the clinical evidence documentation, it's an actual business description of the reason for performing the action.
   */
  reason?: CodeableConcept[];
  /**
   * When an action depends on multiple actions, the meaning is that all actions are dependencies, rather than that any of the actions are a dependency.
   */
  relatedAction?: PlanDefinitionActionRelatedAction[];
  /**
   * Defines the required behavior for the action.
   */
  requiredBehavior?: string;
  _requiredBehavior?: Element;
  /**
   * Defines the selection behavior for the action and its children.
   */
  selectionBehavior?: string;
  _selectionBehavior?: Element;
  /**
   * The subject of an action overrides the subject at a parent action or on the root of the PlanDefinition if specified.
   * In addition, because the subject needs to be resolved during realization, use of subjects in actions (or in the ActivityDefinition referenced by the action) resolves based on the set of subjects supplied in context and by type (i.e. the patient subject would resolve to a resource of type Patient).
   */
  subjectCodeableConcept?: CodeableConcept;
  /**
   * The subject of an action overrides the subject at a parent action or on the root of the PlanDefinition if specified.
   * In addition, because the subject needs to be resolved during realization, use of subjects in actions (or in the ActivityDefinition referenced by the action) resolves based on the set of subjects supplied in context and by type (i.e. the patient subject would resolve to a resource of type Patient).
   */
  subjectReference?: Reference;
  /**
   * A text equivalent of the action to be performed. This provides a human-interpretable description of the action when the definition is consumed by a system that might not be capable of interpreting it dynamically.
   */
  textEquivalent?: string;
  _textEquivalent?: Element;
  /**
   * An optional value describing when the action should be performed.
   */
  timingDateTime?: string;
  _timingDateTime?: Element;
  /**
   * An optional value describing when the action should be performed.
   */
  timingAge?: Age;
  /**
   * An optional value describing when the action should be performed.
   */
  timingPeriod?: Period;
  /**
   * An optional value describing when the action should be performed.
   */
  timingDuration?: Duration;
  /**
   * An optional value describing when the action should be performed.
   */
  timingRange?: Range;
  /**
   * An optional value describing when the action should be performed.
   */
  timingTiming?: Timing;
  /**
   * The title of the action displayed to a user.
   */
  title?: string;
  _title?: Element;
  /**
   * Note that when a referenced ActivityDefinition also defines a transform, the transform specified here generally takes precedence. In addition, if both a transform and dynamic values are specific, the dynamic values are applied to the result of the transform.
   */
  transform?: string;
  _transform?: Element;
  /**
   * A description of when the action should be triggered.
   */
  trigger?: TriggerDefinition[];
  /**
   * The type of action to perform (create, update, remove).
   */
  type?: CodeableConcept;
}
/**
 * Code Values for the PlanDefinition.action.cardinalityBehavior field
 */
export enum PlanDefinitionActionCardinalityBehaviorCodes {
  SINGLE = 'single',
  MULTIPLE = 'multiple',
}
/**
 * Code Values for the PlanDefinition.action.groupingBehavior field
 */
export enum PlanDefinitionActionGroupingBehaviorCodes {
  VISUAL_GROUP = 'visual-group',
  LOGICAL_GROUP = 'logical-group',
  SENTENCE_GROUP = 'sentence-group',
}
/**
 * Code Values for the PlanDefinition.action.precheckBehavior field
 */
export enum PlanDefinitionActionPrecheckBehaviorCodes {
  YES = 'yes',
  NO = 'no',
}
/**
 * Code Values for the PlanDefinition.action.priority field
 */
export enum PlanDefinitionActionPriorityCodes {
  ROUTINE = 'routine',
  URGENT = 'urgent',
  ASAP = 'asap',
  STAT = 'stat',
}
/**
 * Code Values for the PlanDefinition.action.requiredBehavior field
 */
export enum PlanDefinitionActionRequiredBehaviorCodes {
  MUST = 'must',
  COULD = 'could',
  MUST_UNLESS_DOCUMENTED = 'must-unless-documented',
}
/**
 * Code Values for the PlanDefinition.action.selectionBehavior field
 */
export enum PlanDefinitionActionSelectionBehaviorCodes {
  ANY = 'any',
  ALL = 'all',
  ALL_OR_NONE = 'all-or-none',
  EXACTLY_ONE = 'exactly-one',
  AT_MOST_ONE = 'at-most-one',
  ONE_OR_MORE = 'one-or-more',
}
/**
 * This resource allows for the definition of various types of plans as a sharable, consumable, and executable artifact. The resource is general enough to support the description of a broad range of clinical artifacts such as clinical decision support rules, order sets and protocols.
 */
export interface PlanDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'PlanDefinition';
  /**
   * Note that there is overlap between many of the elements defined here and the ActivityDefinition resource. When an ActivityDefinition is referenced (using the definition element), the overlapping elements in the plan override the content of the referenced ActivityDefinition unless otherwise documented in the specific elements. See the PlanDefinition resource for more detailed information.
   */
  action?: PlanDefinitionAction[];
  /**
   * The 'date' element may be more recent than the approval date because of minor changes or editorial corrections.
   */
  approvalDate?: string;
  _approvalDate?: Element;
  /**
   * An individiual or organization primarily involved in the creation and maintenance of the content.
   */
  author?: ContactDetail[];
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the plan definition and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the plan definition.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the plan definition. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the plan definition was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the plan definition as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the plan definition is presumed to be the predominant language in the place the plan definition was created).
   */
  description?: string;
  _description?: Element;
  /**
   * An individual or organization primarily responsible for internal coherence of the content.
   */
  editor?: ContactDetail[];
  /**
   * The effective period for a plan definition  determines when the content is applicable for usage and is independent of publication and review dates. For example, a measure intended to be used for the year 2016 might be published in 2015.
   */
  effectivePeriod?: Period;
  /**
   * An individual or organization responsible for officially endorsing the content for use in some setting.
   */
  endorser?: ContactDetail[];
  /**
   * Allows filtering of plan definitions that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Goals that describe what the activities within the plan are intended to achieve. For example, weight loss, restoring an activity of daily living, obtaining herd immunity via immunization, meeting a process improvement objective, etc.
   */
  goal?: PlanDefinitionGoal[];
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this plan definition outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * It may be possible for the plan definition to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * If specified, this date follows the original approval date.
   */
  lastReviewDate?: string;
  _lastReviewDate?: Element;
  /**
   * A reference to a Library resource containing any formal logic used by the plan definition.
   */
  library?: string[];
  _library?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the plan definition is the organization or individual primarily responsible for the maintenance and upkeep of the plan definition. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the plan definition. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the plan definition. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this plan definition.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Each related artifact is either an attachment, or a reference to another resource, but not both.
   */
  relatedArtifact?: RelatedArtifact[];
  /**
   * An individual or organization primarily responsible for review of some aspect of the content.
   */
  reviewer?: ContactDetail[];
  /**
   * Allows filtering of plan definitions that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * A code or group definition that describes the intended subject of the plan definition.
   */
  subjectCodeableConcept?: CodeableConcept;
  /**
   * A code or group definition that describes the intended subject of the plan definition.
   */
  subjectReference?: Reference;
  /**
   * An explanatory or alternate title for the plan definition giving additional information about its content.
   */
  subtitle?: string;
  _subtitle?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Descriptive topics related to the content of the plan definition. Topics provide a high-level categorization of the definition that can be useful for filtering and searching.
   */
  topic?: CodeableConcept[];
  /**
   * A high-level category for the plan definition that distinguishes the kinds of systems that would be interested in the plan definition.
   */
  type?: CodeableConcept;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * A detailed description of how the plan definition is used from a clinical perspective.
   */
  usage?: string;
  _usage?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different plan definition instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the plan definition with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the PlanDefinition.status field
 */
export enum PlanDefinitionStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * The official certifications, training, and licenses that authorize or otherwise pertain to the provision of care by the practitioner.  For example, a medical license issued by a medical board authorizing the practitioner to practice medicine within a certian locality.
 */
export interface PractitionerQualification extends BackboneElement {
  /**
   * Coded representation of the qualification.
   */
  code: CodeableConcept;
  /**
   * An identifier that applies to this person's qualification in this role.
   */
  identifier?: Identifier[];
  /**
   * Organization that regulates and issues the qualification.
   */
  issuer?: Reference;
  /**
   * Period during which the qualification is valid.
   */
  period?: Period;
}
/**
 * A person who is directly or indirectly involved in the provisioning of healthcare.
 */
export interface Practitioner extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Practitioner';
  /**
   * If the practitioner is not in use by one organization, then it should mark the period on the PractitonerRole with an end date (even if they are active) as they may be active in another role.
   */
  active?: boolean;
  _active?: Element;
  /**
   * The PractitionerRole does not have an address value on it, as it is expected that the location property be used for this purpose (which has an address).
   */
  address?: Address[];
  /**
   * The date of birth for the practitioner.
   */
  birthDate?: string;
  _birthDate?: Element;
  /**
   * The structure aa-BB with this exact casing is one the most widely used notations for locale. However not all systems code this but instead have it as free text. Hence CodeableConcept instead of code as the data type.
   */
  communication?: CodeableConcept[];
  /**
   * Administrative Gender - the gender that the person is considered to have for administration and record keeping purposes.
   */
  gender?: string;
  _gender?: Element;
  /**
   * An identifier that applies to this person in this role.
   */
  identifier?: Identifier[];
  /**
   * The selection of the use property should ensure that there is a single usual name specified, and others use the nickname (alias), old, or other values as appropriate.
   * In general, select the value to be used in the ResourceReference.display based on this:
   * 1. There is more than 1 name
   * 2. Use = usual
   * 3. Period is current to the date of the usage
   * 4. Use = official
   * 5. Other order as decided by internal business rules.
   */
  name?: HumanName[];
  /**
   * Image of the person.
   */
  photo?: Attachment[];
  /**
   * The official certifications, training, and licenses that authorize or otherwise pertain to the provision of care by the practitioner.  For example, a medical license issued by a medical board authorizing the practitioner to practice medicine within a certian locality.
   */
  qualification?: PractitionerQualification[];
  /**
   * Person may have multiple ways to be contacted with different uses or applicable periods.  May need to have options for contacting the person urgently and to help with identification.  These typically will have home numbers, or mobile numbers that are not role specific.
   */
  telecom?: ContactPoint[];
}
/**
 * Code Values for the Practitioner.gender field
 */
export enum PractitionerGenderCodes {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
  UNKNOWN = 'unknown',
}
/**
 * More detailed availability information may be provided in associated Schedule/Slot resources.
 */
export interface PractitionerRoleAvailableTime extends BackboneElement {
  /**
   * Is this always available? (hence times are irrelevant) e.g. 24 hour service.
   */
  allDay?: boolean;
  _allDay?: Element;
  /**
   * The timezone is expected to be for where this HealthcareService is provided at.
   */
  availableEndTime?: string;
  _availableEndTime?: Element;
  /**
   * The timezone is expected to be for where this HealthcareService is provided at.
   */
  availableStartTime?: string;
  _availableStartTime?: Element;
  /**
   * Indicates which days of the week are available between the start and end Times.
   */
  daysOfWeek?: string[];
  _daysOfWeek?: Element;
}
/**
 * Code Values for the PractitionerRole.availableTime.daysOfWeek field
 */
export enum PractitionerRoleAvailableTimeDaysOfWeekCodes {
  MON = 'mon',
  TUE = 'tue',
  WED = 'wed',
  THU = 'thu',
  FRI = 'fri',
  SAT = 'sat',
  SUN = 'sun',
}
/**
 * The practitioner is not available or performing this role during this period of time due to the provided reason.
 */
export interface PractitionerRoleNotAvailable extends BackboneElement {
  /**
   * The reason that can be presented to the user as to why this time is not available.
   */
  description: string;
  _description?: Element;
  /**
   * Service is not available (seasonally or for a public holiday) from this date.
   */
  during?: Period;
}
/**
 * A specific set of Roles/Locations/specialties/services that a practitioner may perform at an organization for a period of time.
 */
export interface PractitionerRole extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'PractitionerRole';
  /**
   * If this value is false, you may refer to the period to see when the role was in active use. If there is no period specified, no inference can be made about when it was active.
   */
  active?: boolean;
  _active?: Element;
  /**
   * A description of site availability exceptions, e.g. public holiday availability. Succinctly describing all possible exceptions to normal site availability as details in the available Times and not available Times.
   */
  availabilityExceptions?: string;
  _availabilityExceptions?: Element;
  /**
   * More detailed availability information may be provided in associated Schedule/Slot resources.
   */
  availableTime?: PractitionerRoleAvailableTime[];
  /**
   * A person may have more than one role.
   */
  code?: CodeableConcept[];
  /**
   * Technical endpoints providing access to services operated for the practitioner with this role.
   */
  endpoint?: Reference[];
  /**
   * The list of healthcare services that this worker provides for this role's Organization/Location(s).
   */
  healthcareService?: Reference[];
  /**
   * Business Identifiers that are specific to a role/location.
   */
  identifier?: Identifier[];
  /**
   * The location(s) at which this practitioner provides care.
   */
  location?: Reference[];
  /**
   * The practitioner is not available or performing this role during this period of time due to the provided reason.
   */
  notAvailable?: PractitionerRoleNotAvailable[];
  /**
   * The organization where the Practitioner performs the roles associated.
   */
  organization?: Reference;
  /**
   * The period during which the person is authorized to act as a practitioner in these role(s) for the organization.
   */
  period?: Period;
  /**
   * Practitioner that is able to provide the defined services for the organization.
   */
  practitioner?: Reference;
  /**
   * Specific specialty of the practitioner.
   */
  specialty?: CodeableConcept[];
  /**
   * Contact details that are specific to the role/location/service.
   */
  telecom?: ContactPoint[];
}
/**
 * Limited to "real" people rather than equipment.
 */
export interface ProcedurePerformer extends BackboneElement {
  /**
   * The practitioner who was involved in the procedure.
   */
  actor: Reference;
  /**
   * Distinguishes the type of involvement of the performer in the procedure. For example, surgeon, anaesthetist, endoscopist.
   */
  function?: CodeableConcept;
  /**
   * The organization the device or practitioner was acting on behalf of.
   */
  onBehalfOf?: Reference;
}
/**
 * A device that is implanted, removed or otherwise manipulated (calibration, battery replacement, fitting a prosthesis, attaching a wound-vac, etc.) as a focal portion of the Procedure.
 */
export interface ProcedureFocalDevice extends BackboneElement {
  /**
   * The kind of change that happened to the device during the procedure.
   */
  action?: CodeableConcept;
  /**
   * The device that was manipulated (changed) during the procedure.
   */
  manipulated: Reference;
}
/**
 * An action that is or was performed on or for a patient. This can be a physical intervention like an operation, or less invasive like long term services, counseling, or hypnotherapy.
 */
export interface Procedure extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Procedure';
  /**
   * Individual who is making the procedure statement.
   */
  asserter?: Reference;
  /**
   * A reference to a resource that contains details of the request for this procedure.
   */
  basedOn?: Reference[];
  /**
   * If the use case requires attributes from the BodySite resource (e.g. to identify and track separately) then use the standard extension [procedure-targetbodystructure](extension-procedure-targetbodystructure.html).
   */
  bodySite?: CodeableConcept[];
  /**
   * A code that classifies the procedure for searching, sorting and display purposes (e.g. "Surgical Procedure").
   */
  category?: CodeableConcept;
  /**
   * The specific procedure that is performed. Use text if the exact nature of the procedure cannot be coded (e.g. "Laparoscopic Appendectomy").
   */
  code?: CodeableConcept;
  /**
   * If complications are only expressed by the narrative text, they can be captured using the CodeableConcept.text.
   */
  complication?: CodeableConcept[];
  /**
   * Any complications that occurred during the procedure, or in the immediate post-performance period.
   */
  complicationDetail?: Reference[];
  /**
   * This will typically be the encounter the event occurred within, but some activities may be initiated prior to or after the official completion of an encounter but still be tied to the context of the encounter.
   */
  encounter?: Reference;
  /**
   * A device that is implanted, removed or otherwise manipulated (calibration, battery replacement, fitting a prosthesis, attaching a wound-vac, etc.) as a focal portion of the Procedure.
   */
  focalDevice?: ProcedureFocalDevice[];
  /**
   * If the procedure required specific follow up - e.g. removal of sutures. The follow up may be represented as a simple note or could potentially be more complex, in which case the CarePlan resource can be used.
   */
  followUp?: CodeableConcept[];
  /**
   * This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and Person resource instances might share the same social insurance number.
   */
  identifier?: Identifier[];
  /**
   * The URL pointing to a FHIR-defined protocol, guideline, order set or other definition that is adhered to in whole or in part by this Procedure.
   */
  instantiatesCanonical?: string[];
  _instantiatesCanonical?: Element;
  /**
   * This might be an HTML page, PDF, etc. or could just be a non-resolvable URI identifier.
   */
  instantiatesUri?: string[];
  _instantiatesUri?: Element;
  /**
   * The location where the procedure actually happened.  E.g. a newborn at home, a tracheostomy at a restaurant.
   */
  location?: Reference;
  /**
   * Any other notes and comments about the procedure.
   */
  note?: Annotation[];
  /**
   * If outcome contains narrative text only, it can be captured using the CodeableConcept.text.
   */
  outcome?: CodeableConcept;
  /**
   * The MedicationAdministration resource has a partOf reference to Procedure, but this is not a circular reference.   For example, the anesthesia MedicationAdministration is part of the surgical Procedure (MedicationAdministration.partOf = Procedure).  For example, the procedure to insert the IV port for an IV medication administration is part of the medication administration (Procedure.partOf = MedicationAdministration).
   */
  partOf?: Reference[];
  /**
   * Age is generally used when the patient reports an age at which the procedure was performed. Range is generally used when the patient reports an age range when the procedure was performed, such as sometime between 20-25 years old.  dateTime supports a range of precision due to some procedures being reported as past procedures that might not have millisecond precision while other procedures performed and documented during the encounter might have more precise UTC timestamps with timezone.
   */
  performedDateTime?: string;
  _performedDateTime?: Element;
  /**
   * Age is generally used when the patient reports an age at which the procedure was performed. Range is generally used when the patient reports an age range when the procedure was performed, such as sometime between 20-25 years old.  dateTime supports a range of precision due to some procedures being reported as past procedures that might not have millisecond precision while other procedures performed and documented during the encounter might have more precise UTC timestamps with timezone.
   */
  performedPeriod?: Period;
  /**
   * Age is generally used when the patient reports an age at which the procedure was performed. Range is generally used when the patient reports an age range when the procedure was performed, such as sometime between 20-25 years old.  dateTime supports a range of precision due to some procedures being reported as past procedures that might not have millisecond precision while other procedures performed and documented during the encounter might have more precise UTC timestamps with timezone.
   */
  performedString?: string;
  _performedString?: Element;
  /**
   * Age is generally used when the patient reports an age at which the procedure was performed. Range is generally used when the patient reports an age range when the procedure was performed, such as sometime between 20-25 years old.  dateTime supports a range of precision due to some procedures being reported as past procedures that might not have millisecond precision while other procedures performed and documented during the encounter might have more precise UTC timestamps with timezone.
   */
  performedAge?: Age;
  /**
   * Age is generally used when the patient reports an age at which the procedure was performed. Range is generally used when the patient reports an age range when the procedure was performed, such as sometime between 20-25 years old.  dateTime supports a range of precision due to some procedures being reported as past procedures that might not have millisecond precision while other procedures performed and documented during the encounter might have more precise UTC timestamps with timezone.
   */
  performedRange?: Range;
  /**
   * Limited to "real" people rather than equipment.
   */
  performer?: ProcedurePerformer[];
  /**
   * Use Procedure.reasonCode when a code sufficiently describes the reason.  Use Procedure.reasonReference when referencing a resource, which allows more information to be conveyed, such as onset date. Procedure.reasonCode and Procedure.reasonReference are not meant to be duplicative.  For a single reason, either Procedure.reasonCode or Procedure.reasonReference can be used.  Procedure.reasonCode may be a summary code, or Procedure.reasonReference may be used to reference a very precise definition of the reason using Condition | Observation | Procedure | DiagnosticReport | DocumentReference.  Both Procedure.reasonCode and Procedure.reasonReference can be used if they are describing different reasons for the procedure.
   */
  reasonCode?: CodeableConcept[];
  /**
   * It is possible for a procedure to be a reason (such as C-Section) for another procedure (such as an epidural). Other examples include endoscopy for dilatation and biopsy (a combination of diagnostic and therapeutic use).
   * Use Procedure.reasonCode when a code sufficiently describes the reason.  Use Procedure.reasonReference when referencing a resource, which allows more information to be conveyed, such as onset date. Procedure.reasonCode and Procedure.reasonReference are not meant to be duplicative.  For a single reason, either Procedure.reasonCode or Procedure.reasonReference can be used.  Procedure.reasonCode may be a summary code, or Procedure.reasonReference may be used to reference a very precise definition of the reason using Condition | Observation | Procedure | DiagnosticReport | DocumentReference.  Both Procedure.reasonCode and Procedure.reasonReference can be used if they are describing different reasons for the procedure.
   */
  reasonReference?: Reference[];
  /**
   * Individual who recorded the record and takes responsibility for its content.
   */
  recorder?: Reference;
  /**
   * There could potentially be multiple reports - e.g. if this was a procedure which took multiple biopsies resulting in a number of anatomical pathology reports.
   */
  report?: Reference[];
  /**
   * The "unknown" code is not to be used to convey other statuses.  The "unknown" code should be used when one of the statuses applies, but the authoring system doesn't know the current state of the procedure.
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * This is generally only used for "exception" statuses such as "not-done", "suspended" or "aborted". The reason for performing the event at all is captured in reasonCode, not here.
   */
  statusReason?: CodeableConcept;
  /**
   * The person, animal or group on which the procedure was performed.
   */
  subject: Reference;
  /**
   * For devices actually implanted or removed, use Procedure.device.
   */
  usedCode?: CodeableConcept[];
  /**
   * For devices actually implanted or removed, use Procedure.device.
   */
  usedReference?: Reference[];
}
/**
 * Code Values for the Procedure.status field
 */
export enum ProcedureStatusCodes {
  PREPARATION = 'preparation',
  IN_PROGRESS = 'in-progress',
  NOT_DONE = 'not-done',
  ON_HOLD = 'on-hold',
  STOPPED = 'stopped',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  UNKNOWN = 'unknown',
}
/**
 * Several agents may be associated (i.e. has some responsibility for an activity) with an activity and vice-versa.
 */
export interface ProvenanceAgent extends BackboneElement {
  /**
   * onBehalfOfIdentity should be used when the agent is not a Resource type.
   */
  onBehalfOf?: Reference;
  /**
   * For example: doctor, nurse, clerk, etc.
   */
  role?: CodeableConcept[];
  /**
   * For example: author, performer, enterer, attester, etc.
   */
  type?: CodeableConcept;
  /**
   * whoIdentity should be used when the agent is not a Resource type.
   */
  who: Reference;
}
/**
 * An entity used in this activity.
 */
export interface ProvenanceEntity extends BackboneElement {
  /**
   * A usecase where one Provenance.entity.agent is used where the Entity that was used in the creation/updating of the Target, is not in the context of the same custodianship as the Target, and thus the meaning of Provenance.entity.agent is to say that the entity referenced is managed elsewhere and that this Agent provided access to it.  This would be similar to where the Entity being referenced is managed outside FHIR, such as through HL7 v2, v3, or XDS. This might be where the Entity being referenced is managed in another FHIR resource server. Thus it explains the Provenance of that Entity's use in the context of this Provenance activity.
   */
  agent?: ProvenanceAgent[];
  /**
   * How the entity was used during the activity.
   */
  role: string;
  _role?: Element;
  /**
   * whatIdentity should be used for entities that are not a Resource type.
   */
  what: Reference;
}
/**
 * Code Values for the Provenance.entity.role field
 */
export enum ProvenanceEntityRoleCodes {
  DERIVATION = 'derivation',
  REVISION = 'revision',
  QUOTATION = 'quotation',
  SOURCE = 'source',
  REMOVAL = 'removal',
}
/**
 * Provenance of a resource is a record that describes entities and processes involved in producing and delivering or otherwise influencing that resource. Provenance provides a critical foundation for assessing authenticity, enabling trust, and allowing reproducibility. Provenance assertions are a form of contextual metadata and can themselves become important records with their own provenance. Provenance statement indicates clinical significance in terms of confidence in authenticity, reliability, and trustworthiness, integrity, and stage in lifecycle (e.g. Document Completion - has the artifact been legally authenticated), all of which may impact security, privacy, and trust policies.
 */
export interface Provenance extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Provenance';
  /**
   * An activity is something that occurs over a period of time and acts upon or with entities; it may include consuming, processing, transforming, modifying, relocating, using, or generating entities.
   */
  activity?: CodeableConcept;
  /**
   * Several agents may be associated (i.e. has some responsibility for an activity) with an activity and vice-versa.
   */
  agent: ProvenanceAgent[];
  /**
   * An entity used in this activity.
   */
  entity?: ProvenanceEntity[];
  /**
   * Where the activity occurred, if relevant.
   */
  location?: Reference;
  /**
   * The period can be a little arbitrary; where possible, the time should correspond to human assessment of the activity time.
   */
  occurredPeriod?: Period;
  /**
   * The period can be a little arbitrary; where possible, the time should correspond to human assessment of the activity time.
   */
  occurredDateTime?: string;
  _occurredDateTime?: Element;
  /**
   * For example: Where an OAuth token authorizes, the unique identifier from the OAuth token is placed into the policy element Where a policy engine (e.g. XACML) holds policy logic, the unique policy identifier is placed into the policy element.
   */
  policy?: string[];
  _policy?: Element;
  /**
   * The reason that the activity was taking place.
   */
  reason?: CodeableConcept[];
  /**
   * This can be a little different from the time stamp on the resource if there is a delay between recording the event and updating the provenance and target resource.
   */
  recorded: string;
  _recorded?: Element;
  /**
   * A digital signature on the target Reference(s). The signer should match a Provenance.agent. The purpose of the signature is indicated.
   */
  signature?: Signature[];
  /**
   * Target references are usually version specific, but might not be, if a version has not been assigned or if the provenance information is part of the set of resources being maintained (i.e. a document). When using the RESTful API, the identity of the resource might not be known (especially not the version specific one); the client may either submit the resource first, and then the provenance, or it may submit both using a single transaction. See the notes on transaction for further discussion.
   */
  target: Reference[];
}
/**
 * If multiple repetitions of this extension are present, the item should be enabled when the condition for *any* of the repetitions is true.  I.e. treat "enableWhen"s as being joined by an "or" clause.  This element is a modifier because if enableWhen is present for an item, "required" is ignored unless one of the enableWhen conditions is met. When an item is disabled, all of its descendants are disabled, regardless of what their own enableWhen logic might evaluate to.
 */
export interface QuestionnaireItemEnableWhen extends BackboneElement {
  /**
   * A value that the referenced question is tested using the specified operator in order for the item to be enabled.
   */
  answerBoolean: boolean;
  _answerBoolean?: Element;
  /**
   * A value that the referenced question is tested using the specified operator in order for the item to be enabled.
   */
  answerDecimal: number;
  /**
   * A value that the referenced question is tested using the specified operator in order for the item to be enabled.
   */
  answerInteger: number;
  /**
   * A value that the referenced question is tested using the specified operator in order for the item to be enabled.
   */
  answerDate: string;
  _answerDate?: Element;
  /**
   * A value that the referenced question is tested using the specified operator in order for the item to be enabled.
   */
  answerDateTime: string;
  _answerDateTime?: Element;
  /**
   * A value that the referenced question is tested using the specified operator in order for the item to be enabled.
   */
  answerTime: string;
  _answerTime?: Element;
  /**
   * A value that the referenced question is tested using the specified operator in order for the item to be enabled.
   */
  answerString: string;
  _answerString?: Element;
  /**
   * A value that the referenced question is tested using the specified operator in order for the item to be enabled.
   */
  answerCoding: Coding;
  /**
   * A value that the referenced question is tested using the specified operator in order for the item to be enabled.
   */
  answerQuantity: Quantity;
  /**
   * A value that the referenced question is tested using the specified operator in order for the item to be enabled.
   */
  answerReference: Reference;
  /**
   * Specifies the criteria by which the question is enabled.
   */
  operator: string;
  _operator?: Element;
  /**
   * If multiple question occurrences are present for the same question (same linkId), then this refers to the nearest question occurrence reachable by tracing first the "ancestor" axis and then the "preceding" axis and then the "following" axis.
   */
  question: string;
  _question?: Element;
}
/**
 * Code Values for the Questionnaire.item.enableWhen.operator field
 */
export enum QuestionnaireItemEnableWhenOperatorCodes {
  EXISTS = 'exists',
  EQUALS = '=',
  NOT_EQUALS = '!=',
  GREATER_THAN = '>',
  LESS_THAN = '<',
  GREATER_THAN_OR_EQUALS = '>=',
  LESS_THAN_OR_EQUALS = '<=',
}
/**
 * This element can be used when the value set machinery of answerValueSet is deemed too cumbersome or when there's a need to capture possible answers that are not codes.
 */
export interface QuestionnaireItemAnswerOption extends BackboneElement {
  /**
   * Use this instead of initial[v] if answerValueSet is present.
   */
  initialSelected?: boolean;
  _initialSelected?: Element;
  /**
   * The data type of the value must agree with the item.type.
   */
  valueInteger?: number;
  /**
   * The data type of the value must agree with the item.type.
   */
  valueDate?: string;
  _valueDate?: Element;
  /**
   * The data type of the value must agree with the item.type.
   */
  valueTime?: string;
  _valueTime?: Element;
  /**
   * The data type of the value must agree with the item.type.
   */
  valueString?: string;
  _valueString?: Element;
  /**
   * The data type of the value must agree with the item.type.
   */
  valueCoding?: Coding;
  /**
   * The data type of the value must agree with the item.type.
   */
  valueReference?: Reference;
}
/**
 * The user is allowed to change the value and override the default (unless marked as read-only). If the user doesn't change the value, then this initial value will be persisted when the QuestionnaireResponse is initially created.  Note that initial values can influence results.  The data type of initial[x] must agree with the item.type, and only repeating items can have more then one initial value.
 */
export interface QuestionnaireItemInitial extends BackboneElement {
  /**
   * The type of the initial value must be consistent with the type of the item.
   */
  valueBoolean?: boolean;
  _valueBoolean?: Element;
  /**
   * The type of the initial value must be consistent with the type of the item.
   */
  valueDecimal?: number;
  /**
   * The type of the initial value must be consistent with the type of the item.
   */
  valueInteger?: number;
  /**
   * The type of the initial value must be consistent with the type of the item.
   */
  valueDate?: string;
  _valueDate?: Element;
  /**
   * The type of the initial value must be consistent with the type of the item.
   */
  valueDateTime?: string;
  _valueDateTime?: Element;
  /**
   * The type of the initial value must be consistent with the type of the item.
   */
  valueTime?: string;
  _valueTime?: Element;
  /**
   * The type of the initial value must be consistent with the type of the item.
   */
  valueString?: string;
  _valueString?: Element;
  /**
   * The type of the initial value must be consistent with the type of the item.
   */
  valueUri?: string;
  _valueUri?: Element;
  /**
   * The type of the initial value must be consistent with the type of the item.
   */
  valueAttachment?: Attachment;
  /**
   * The type of the initial value must be consistent with the type of the item.
   */
  valueCoding?: Coding;
  /**
   * The type of the initial value must be consistent with the type of the item.
   */
  valueQuantity?: Quantity;
  /**
   * The type of the initial value must be consistent with the type of the item.
   */
  valueReference?: Reference;
}
/**
 * The content of the questionnaire is constructed from an ordered, hierarchical collection of items.
 */
export interface QuestionnaireItem extends BackboneElement {
  /**
   * This element can be used when the value set machinery of answerValueSet is deemed too cumbersome or when there's a need to capture possible answers that are not codes.
   */
  answerOption?: QuestionnaireItemAnswerOption[];
  /**
   * LOINC defines many useful value sets for questionnaire responses. See [LOINC Answer Lists](loinc.html#alist). The value may come from the ElementDefinition referred to by .definition.
   */
  answerValueSet?: string;
  _answerValueSet?: Element;
  /**
   * The value may come from the ElementDefinition referred to by .definition.
   */
  code?: Coding[];
  /**
   * The uri refers to an ElementDefinition in a [StructureDefinition](structuredefinition.html#) and always starts with the [canonical URL](references.html#canonical) for the target resource. When referring to a StructureDefinition, a fragment identifier is used to specify the element definition by its id [Element.id](element-definitions.html#Element.id). E.g. http://hl7.org/fhir/StructureDefinition/Observation#Observation.value[x]. In the absence of a fragment identifier, the first/root element definition in the target is the matching element definition.
   */
  definition?: string;
  _definition?: Element;
  /**
   * This element must be specified if more than one enableWhen value is provided.
   */
  enableBehavior?: string;
  _enableBehavior?: Element;
  /**
   * If multiple repetitions of this extension are present, the item should be enabled when the condition for *any* of the repetitions is true.  I.e. treat "enableWhen"s as being joined by an "or" clause.  This element is a modifier because if enableWhen is present for an item, "required" is ignored unless one of the enableWhen conditions is met. When an item is disabled, all of its descendants are disabled, regardless of what their own enableWhen logic might evaluate to.
   */
  enableWhen?: QuestionnaireItemEnableWhen[];
  /**
   * The user is allowed to change the value and override the default (unless marked as read-only). If the user doesn't change the value, then this initial value will be persisted when the QuestionnaireResponse is initially created.  Note that initial values can influence results.  The data type of initial[x] must agree with the item.type, and only repeating items can have more then one initial value.
   */
  initial?: QuestionnaireItemInitial[];
  /**
   * There is no specified limit to the depth of nesting.  However, Questionnaire authors are encouraged to consider the impact on the user and user interface of overly deep nesting.
   */
  item?: QuestionnaireItem[];
  /**
   * This ''can'' be a meaningful identifier (e.g. a LOINC code) but is not intended to have any meaning.  GUIDs or sequential numbers are appropriate here.
   */
  linkId: string;
  _linkId?: Element;
  /**
   * For base64binary, reflects the number of characters representing the encoded data, not the number of bytes of the binary data. The value may come from the ElementDefinition referred to by .definition.
   */
  maxLength?: number;
  /**
   * These are generally unique within a questionnaire, though this is not guaranteed. Some questionnaires may have multiple questions with the same label with logic to control which gets exposed.  Typically, these won't be used for "display" items, though such use is not prohibited.  Systems SHOULD NOT generate their own prefixes if prefixes are defined for any items within a Questionnaire.
   */
  prefix?: string;
  _prefix?: Element;
  /**
   * The value of readOnly elements can be established by asserting  extensions for defaultValues, linkages that support pre-population and/or extensions that support calculation based on other answers.
   */
  readOnly?: boolean;
  _readOnly?: Element;
  /**
   * If a question is marked as repeats=true, then multiple answers can be provided for the question in the corresponding QuestionnaireResponse.  When rendering the questionnaire, it is up to the rendering software whether to render the question text for each answer repetition (i.e. "repeat the question") or to simply allow entry/selection of multiple answers for the question (repeat the answers).  Which is most appropriate visually may depend on the type of answer as well as whether there are nested items.
   * The resulting QuestionnaireResponse will be populated the same way regardless of rendering - one 'question' item with multiple answer values.
   *  The value may come from the ElementDefinition referred to by .definition.
   */
  repeats?: boolean;
  _repeats?: Element;
  /**
   * Questionnaire.item.required only has meaning for elements that are conditionally enabled with enableWhen if the condition evaluates to true.  If an item that contains other items is marked as required, that does not automatically make the contained elements required (though required groups must contain at least one child element). The value may come from the ElementDefinition referred to by .definition.
   */
  required?: boolean;
  _required?: Element;
  /**
   * When using this element to represent the name of a section, use group type item and also make sure to limit the text element to a short string suitable for display as a section heading.  Group item instructions should be included as a display type item within the group.
   */
  text?: string;
  _text?: Element;
  /**
   * Additional constraints on the type of answer can be conveyed by extensions. The value may come from the ElementDefinition referred to by .definition.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the Questionnaire.item.enableBehavior field
 */
export enum QuestionnaireItemEnableBehaviorCodes {
  ALL = 'all',
  ANY = 'any',
}
/**
 * Code Values for the Questionnaire.item.type field
 */
export enum QuestionnaireItemTypeCodes {
  GROUP = 'group',
  DISPLAY = 'display',
  BOOLEAN = 'boolean',
  DECIMAL = 'decimal',
  INTEGER = 'integer',
  DATE = 'date',
  DATETIME = 'dateTime',
}
/**
 * A structured set of questions intended to guide the collection of answers from end-users. Questionnaires provide detailed control over order, presentation, phraseology and grouping to allow coherent, consistent data collection.
 */
export interface Questionnaire extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Questionnaire';
  /**
   * The 'date' element may be more recent than the approval date because of minor changes or editorial corrections.
   */
  approvalDate?: string;
  _approvalDate?: Element;
  /**
   * An identifier for this question or group of questions in a particular terminology such as LOINC.
   */
  code?: Coding[];
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the questionnaire and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the questionnaire.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the questionnaire. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * The URL of a Questionnaire that this Questionnaire is based on.
   */
  derivedFrom?: string[];
  _derivedFrom?: Element;
  /**
   * This description can be used to capture details such as why the questionnaire was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the questionnaire as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the questionnaire is presumed to be the predominant language in the place the questionnaire was created).
   */
  description?: string;
  _description?: Element;
  /**
   * The effective period for a questionnaire  determines when the content is applicable for usage and is independent of publication and review dates. For example, a measure intended to be used for the year 2016 might be published in 2015.
   */
  effectivePeriod?: Period;
  /**
   * Allows filtering of questionnaires that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this questionnaire outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * The content of the questionnaire is constructed from an ordered, hierarchical collection of items.
   */
  item?: QuestionnaireItem[];
  /**
   * It may be possible for the questionnaire to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * If specified, this date follows the original approval date.
   */
  lastReviewDate?: string;
  _lastReviewDate?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the questionnaire is the organization or individual primarily responsible for the maintenance and upkeep of the questionnaire. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the questionnaire. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the questionnaire. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this questionnaire.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Allows filtering of questionnaires that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * If none are specified, then the subject is unlimited.
   */
  subjectType?: string[];
  _subjectType?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * The name of the referenced questionnaire can be conveyed using the http://hl7.org/fhir/StructureDefinition/display extension.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different questionnaire instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the questionnaire with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the Questionnaire.status field
 */
export enum QuestionnaireStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * The value is nested because we cannot have a repeating structure that has variable type.
 */
export interface QuestionnaireResponseItemAnswer extends BackboneElement {
  /**
   * Nested groups and/or questions found within this particular answer.
   */
  item?: QuestionnaireResponseItem[];
  /**
   * More complex structures (Attachment, Resource and Quantity) will typically be limited to electronic forms that can expose an appropriate user interface to capture the components and enforce the constraints of a complex data type.  Additional complex types can be introduced through extensions. Must match the datatype specified by Questionnaire.item.type in the corresponding Questionnaire.
   */
  valueBoolean?: boolean;
  _valueBoolean?: Element;
  /**
   * More complex structures (Attachment, Resource and Quantity) will typically be limited to electronic forms that can expose an appropriate user interface to capture the components and enforce the constraints of a complex data type.  Additional complex types can be introduced through extensions. Must match the datatype specified by Questionnaire.item.type in the corresponding Questionnaire.
   */
  valueDecimal?: number;
  /**
   * More complex structures (Attachment, Resource and Quantity) will typically be limited to electronic forms that can expose an appropriate user interface to capture the components and enforce the constraints of a complex data type.  Additional complex types can be introduced through extensions. Must match the datatype specified by Questionnaire.item.type in the corresponding Questionnaire.
   */
  valueInteger?: number;
  /**
   * More complex structures (Attachment, Resource and Quantity) will typically be limited to electronic forms that can expose an appropriate user interface to capture the components and enforce the constraints of a complex data type.  Additional complex types can be introduced through extensions. Must match the datatype specified by Questionnaire.item.type in the corresponding Questionnaire.
   */
  valueDate?: string;
  _valueDate?: Element;
  /**
   * More complex structures (Attachment, Resource and Quantity) will typically be limited to electronic forms that can expose an appropriate user interface to capture the components and enforce the constraints of a complex data type.  Additional complex types can be introduced through extensions. Must match the datatype specified by Questionnaire.item.type in the corresponding Questionnaire.
   */
  valueDateTime?: string;
  _valueDateTime?: Element;
  /**
   * More complex structures (Attachment, Resource and Quantity) will typically be limited to electronic forms that can expose an appropriate user interface to capture the components and enforce the constraints of a complex data type.  Additional complex types can be introduced through extensions. Must match the datatype specified by Questionnaire.item.type in the corresponding Questionnaire.
   */
  valueTime?: string;
  _valueTime?: Element;
  /**
   * More complex structures (Attachment, Resource and Quantity) will typically be limited to electronic forms that can expose an appropriate user interface to capture the components and enforce the constraints of a complex data type.  Additional complex types can be introduced through extensions. Must match the datatype specified by Questionnaire.item.type in the corresponding Questionnaire.
   */
  valueString?: string;
  _valueString?: Element;
  /**
   * More complex structures (Attachment, Resource and Quantity) will typically be limited to electronic forms that can expose an appropriate user interface to capture the components and enforce the constraints of a complex data type.  Additional complex types can be introduced through extensions. Must match the datatype specified by Questionnaire.item.type in the corresponding Questionnaire.
   */
  valueUri?: string;
  _valueUri?: Element;
  /**
   * More complex structures (Attachment, Resource and Quantity) will typically be limited to electronic forms that can expose an appropriate user interface to capture the components and enforce the constraints of a complex data type.  Additional complex types can be introduced through extensions. Must match the datatype specified by Questionnaire.item.type in the corresponding Questionnaire.
   */
  valueAttachment?: Attachment;
  /**
   * More complex structures (Attachment, Resource and Quantity) will typically be limited to electronic forms that can expose an appropriate user interface to capture the components and enforce the constraints of a complex data type.  Additional complex types can be introduced through extensions. Must match the datatype specified by Questionnaire.item.type in the corresponding Questionnaire.
   */
  valueCoding?: Coding;
  /**
   * More complex structures (Attachment, Resource and Quantity) will typically be limited to electronic forms that can expose an appropriate user interface to capture the components and enforce the constraints of a complex data type.  Additional complex types can be introduced through extensions. Must match the datatype specified by Questionnaire.item.type in the corresponding Questionnaire.
   */
  valueQuantity?: Quantity;
  /**
   * More complex structures (Attachment, Resource and Quantity) will typically be limited to electronic forms that can expose an appropriate user interface to capture the components and enforce the constraints of a complex data type.  Additional complex types can be introduced through extensions. Must match the datatype specified by Questionnaire.item.type in the corresponding Questionnaire.
   */
  valueReference?: Reference;
}
/**
 * Groups cannot have answers and therefore must nest directly within item. When dealing with questions, nesting must occur within each answer because some questions may have multiple answers (and the nesting occurs for each answer).
 */
export interface QuestionnaireResponseItem extends BackboneElement {
  /**
   * The value is nested because we cannot have a repeating structure that has variable type.
   */
  answer?: QuestionnaireResponseItemAnswer[];
  /**
   * The ElementDefinition must be in a [StructureDefinition](structuredefinition.html#), and must have a fragment identifier that identifies the specific data element by its id (Element.id). E.g. http://hl7.org/fhir/StructureDefinition/Observation#Observation.value[x].
   * There is no need for this element if the item pointed to by the linkId has a definition listed.
   */
  definition?: string;
  _definition?: Element;
  /**
   * Questions or sub-groups nested beneath a question or group.
   */
  item?: QuestionnaireResponseItem[];
  /**
   * The item from the Questionnaire that corresponds to this item in the QuestionnaireResponse resource.
   */
  linkId: string;
  _linkId?: Element;
  /**
   * Text that is displayed above the contents of the group or as the text of the question being answered.
   */
  text?: string;
  _text?: Element;
}
/**
 * A structured set of questions and their answers. The questions are ordered and grouped into coherent subsets, corresponding to the structure of the grouping of the questionnaire being responded to.
 */
export interface QuestionnaireResponse extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'QuestionnaireResponse';
  /**
   * Mapping a subject's answers to multiple choice options and determining what to put in the textual answer is a matter of interpretation.  Authoring by device would indicate that some portion of the questionnaire had been auto-populated.
   */
  author?: Reference;
  /**
   * May be different from the lastUpdateTime of the resource itself, because that reflects when the data was known to the server, not when the data was captured.
   * This element is optional to allow for systems that might not know the value, however it SHOULD be populated if possible.
   */
  authored?: string;
  _authored?: Element;
  /**
   * The order, proposal or plan that is fulfilled in whole or in part by this QuestionnaireResponse.  For example, a ServiceRequest seeking an intake assessment or a decision support recommendation to assess for post-partum depression.
   */
  basedOn?: Reference[];
  /**
   * This will typically be the encounter the event occurred within, but some activities may be initiated prior to or after the official completion of an encounter but still be tied to the context of the encounter. A questionnaire that was initiated during an encounter but not fully completed during the encounter would still generally be associated with the encounter.
   */
  encounter?: Reference;
  /**
   * A business identifier assigned to a particular completed (or partially completed) questionnaire.
   */
  identifier?: Identifier;
  /**
   * Groups cannot have answers and therefore must nest directly within item. When dealing with questions, nesting must occur within each answer because some questions may have multiple answers (and the nesting occurs for each answer).
   */
  item?: QuestionnaireResponseItem[];
  /**
   * Composition of questionnaire responses will be handled by the parent questionnaire having answers that reference the child questionnaire.  For relationships to referrals, and other types of requests, use basedOn.
   */
  partOf?: Reference[];
  /**
   * If a QuestionnaireResponse references a Questionnaire, then the QuestionnaireResponse structure must be consistent with the Questionnaire (i.e. questions must be organized into the same groups, nested questions must still be nested, etc.).
   */
  questionnaire?: string;
  _questionnaire?: Element;
  /**
   * If not specified, no inference can be made about who provided the data.
   */
  source?: Reference;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * If the Questionnaire declared a subjectType, the resource pointed to by this element must be an instance of one of the listed types.
   */
  subject?: Reference;
}
/**
 * Code Values for the QuestionnaireResponse.status field
 */
export enum QuestionnaireResponseStatusCodes {
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  AMENDED = 'amended',
  ENTERED_IN_ERROR = 'entered-in-error',
  STOPPED = 'stopped',
}
/**
 * If no language is specified, this *implies* that the default local language is spoken.  If you need to convey proficiency for multiple modes, then you need multiple RelatedPerson.Communication associations.   If the RelatedPerson does not speak the default local language, then the Interpreter Required Standard can be used to explicitly declare that an interpreter is required.
 */
export interface RelatedPersonCommunication extends BackboneElement {
  /**
   * The structure aa-BB with this exact casing is one the most widely used notations for locale. However not all systems actually code this but instead have it as free text. Hence CodeableConcept instead of code as the data type.
   */
  language: CodeableConcept;
  /**
   * This language is specifically identified for communicating healthcare information.
   */
  preferred?: boolean;
  _preferred?: Element;
}
/**
 * Information about a person that is involved in the care for a patient, but who is not the target of healthcare, nor has a formal responsibility in the care process.
 */
export interface RelatedPerson extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'RelatedPerson';
  /**
   * This element is labeled as a modifier because it may be used to mark that the resource was created in error.
   */
  active?: boolean;
  _active?: Element;
  /**
   * Address where the related person can be contacted or visited.
   */
  address?: Address[];
  /**
   * The date on which the related person was born.
   */
  birthDate?: string;
  _birthDate?: Element;
  /**
   * If no language is specified, this *implies* that the default local language is spoken.  If you need to convey proficiency for multiple modes, then you need multiple RelatedPerson.Communication associations.   If the RelatedPerson does not speak the default local language, then the Interpreter Required Standard can be used to explicitly declare that an interpreter is required.
   */
  communication?: RelatedPersonCommunication[];
  /**
   * Administrative Gender - the gender that the person is considered to have for administration and record keeping purposes.
   */
  gender?: string;
  _gender?: Element;
  /**
   * Identifier for a person within a particular scope.
   */
  identifier?: Identifier[];
  /**
   * A name associated with the person.
   */
  name?: HumanName[];
  /**
   * The patient this person is related to.
   */
  patient: Reference;
  /**
   * The period of time during which this relationship is or was active. If there are no dates defined, then the interval is unknown.
   */
  period?: Period;
  /**
   * Image of the person.
   */
  photo?: Attachment[];
  /**
   * The nature of the relationship between a patient and the related person.
   */
  relationship?: CodeableConcept[];
  /**
   * Person may have multiple ways to be contacted with different uses or applicable periods.  May need to have options for contacting the person urgently, and also to help with identification.
   */
  telecom?: ContactPoint[];
}
/**
 * Code Values for the RelatedPerson.gender field
 */
export enum RelatedPersonGenderCodes {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
  UNKNOWN = 'unknown',
}
/**
 * When multiple conditions of the same kind are present, the effects are combined using AND semantics, so the overall condition is true only if all of the conditions are true.
 */
export interface RequestGroupActionCondition extends BackboneElement {
  /**
   * The expression may be inlined, or may be a reference to a named expression within a logic library referenced by the library element.
   */
  expression?: Expression;
  /**
   * Applicability criteria are used to determine immediate applicability when a plan definition is applied to a given context. Start and stop criteria are carried through application and used to describe enter/exit criteria for an action.
   */
  kind: string;
  _kind?: Element;
}
/**
 * Code Values for the RequestGroup.action.condition.kind field
 */
export enum RequestGroupActionConditionKindCodes {
  APPLICABILITY = 'applicability',
  START = 'start',
  STOP = 'stop',
}
/**
 * A relationship to another action such as "before" or "30-60 minutes after start of".
 */
export interface RequestGroupActionRelatedAction extends BackboneElement {
  /**
   * The element id of the action this is related to.
   */
  actionId: string;
  _actionId?: Element;
  /**
   * A duration or range of durations to apply to the relationship. For example, 30-60 minutes before.
   */
  offsetDuration?: Duration;
  /**
   * A duration or range of durations to apply to the relationship. For example, 30-60 minutes before.
   */
  offsetRange?: Range;
  /**
   * The relationship of this action to the related action.
   */
  relationship: string;
  _relationship?: Element;
}
/**
 * Code Values for the RequestGroup.action.relatedAction.relationship field
 */
export enum RequestGroupActionRelatedActionRelationshipCodes {
  BEFORE_START = 'before-start',
  BEFORE = 'before',
  BEFORE_END = 'before-end',
  CONCURRENT_WITH_START = 'concurrent-with-start',
  CONCURRENT = 'concurrent',
  CONCURRENT_WITH_END = 'concurrent-with-end',
  AFTER_START = 'after-start',
  AFTER = 'after',
  AFTER_END = 'after-end',
}
/**
 * The actions, if any, produced by the evaluation of the artifact.
 */
export interface RequestGroupAction extends BackboneElement {
  /**
   * Sub actions.
   */
  action?: RequestGroupAction[];
  /**
   * Defines whether the action can be selected multiple times.
   */
  cardinalityBehavior?: string;
  _cardinalityBehavior?: Element;
  /**
   * A code that provides meaning for the action or action group. For example, a section may have a LOINC code for a section of a documentation template.
   */
  code?: CodeableConcept[];
  /**
   * When multiple conditions of the same kind are present, the effects are combined using AND semantics, so the overall condition is true only if all of the conditions are true.
   */
  condition?: RequestGroupActionCondition[];
  /**
   * A short description of the action used to provide a summary to display to the user.
   */
  description?: string;
  _description?: Element;
  /**
   * Didactic or other informational resources associated with the action that can be provided to the CDS recipient. Information resources can include inline text commentary and links to web resources.
   */
  documentation?: RelatedArtifact[];
  /**
   * Defines the grouping behavior for the action and its children.
   */
  groupingBehavior?: string;
  _groupingBehavior?: Element;
  /**
   * The participant that should perform or be responsible for this action.
   */
  participant?: Reference[];
  /**
   * Defines whether the action should usually be preselected.
   */
  precheckBehavior?: string;
  _precheckBehavior?: Element;
  /**
   * A user-visible prefix for the action.
   */
  prefix?: string;
  _prefix?: Element;
  /**
   * Indicates how quickly the action should be addressed with respect to other actions.
   */
  priority?: string;
  _priority?: Element;
  /**
   * A relationship to another action such as "before" or "30-60 minutes after start of".
   */
  relatedAction?: RequestGroupActionRelatedAction[];
  /**
   * Defines expectations around whether an action is required.
   */
  requiredBehavior?: string;
  _requiredBehavior?: Element;
  /**
   * The target resource SHALL be a [Request](request.html) resource with a Request.intent set to "option".
   */
  resource?: Reference;
  /**
   * Defines the selection behavior for the action and its children.
   */
  selectionBehavior?: string;
  _selectionBehavior?: Element;
  /**
   * A text equivalent of the action to be performed. This provides a human-interpretable description of the action when the definition is consumed by a system that might not be capable of interpreting it dynamically.
   */
  textEquivalent?: string;
  _textEquivalent?: Element;
  /**
   * An optional value describing when the action should be performed.
   */
  timingDateTime?: string;
  _timingDateTime?: Element;
  /**
   * An optional value describing when the action should be performed.
   */
  timingAge?: Age;
  /**
   * An optional value describing when the action should be performed.
   */
  timingPeriod?: Period;
  /**
   * An optional value describing when the action should be performed.
   */
  timingDuration?: Duration;
  /**
   * An optional value describing when the action should be performed.
   */
  timingRange?: Range;
  /**
   * An optional value describing when the action should be performed.
   */
  timingTiming?: Timing;
  /**
   * The title of the action displayed to a user.
   */
  title?: string;
  _title?: Element;
  /**
   * The type of action to perform (create, update, remove).
   */
  type?: CodeableConcept;
}
/**
 * Code Values for the RequestGroup.action.cardinalityBehavior field
 */
export enum RequestGroupActionCardinalityBehaviorCodes {
  SINGLE = 'single',
  MULTIPLE = 'multiple',
}
/**
 * Code Values for the RequestGroup.action.groupingBehavior field
 */
export enum RequestGroupActionGroupingBehaviorCodes {
  VISUAL_GROUP = 'visual-group',
  LOGICAL_GROUP = 'logical-group',
  SENTENCE_GROUP = 'sentence-group',
}
/**
 * Code Values for the RequestGroup.action.precheckBehavior field
 */
export enum RequestGroupActionPrecheckBehaviorCodes {
  YES = 'yes',
  NO = 'no',
}
/**
 * Code Values for the RequestGroup.action.priority field
 */
export enum RequestGroupActionPriorityCodes {
  ROUTINE = 'routine',
  URGENT = 'urgent',
  ASAP = 'asap',
  STAT = 'stat',
}
/**
 * Code Values for the RequestGroup.action.requiredBehavior field
 */
export enum RequestGroupActionRequiredBehaviorCodes {
  MUST = 'must',
  COULD = 'could',
  MUST_UNLESS_DOCUMENTED = 'must-unless-documented',
}
/**
 * Code Values for the RequestGroup.action.selectionBehavior field
 */
export enum RequestGroupActionSelectionBehaviorCodes {
  ANY = 'any',
  ALL = 'all',
  ALL_OR_NONE = 'all-or-none',
  EXACTLY_ONE = 'exactly-one',
  AT_MOST_ONE = 'at-most-one',
  ONE_OR_MORE = 'one-or-more',
}
/**
 * A group of related requests that can be used to capture intended activities that have inter-dependencies such as "give this medication after that one".
 */
export interface RequestGroup extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'RequestGroup';
  /**
   * The actions, if any, produced by the evaluation of the artifact.
   */
  action?: RequestGroupAction[];
  /**
   * Provides a reference to the author of the request group.
   */
  author?: Reference;
  /**
   * Indicates when the request group was created.
   */
  authoredOn?: string;
  _authoredOn?: Element;
  /**
   * A plan, proposal or order that is fulfilled in whole or in part by this request.
   */
  basedOn?: Reference[];
  /**
   * This element can be used to provide a code that captures the meaning of the request group as a whole, as opposed to the code of the action element, which captures the meaning of the individual actions within the request group.
   */
  code?: CodeableConcept;
  /**
   * Describes the context of the request group, if any.
   */
  encounter?: Reference;
  /**
   * Requests are linked either by a "basedOn" relationship (i.e. one request is fulfilling another) or by having a common requisition.  Requests that are part of the same requisition are generally treated independently from the perspective of changing their state or maintaining them after initial creation.
   */
  groupIdentifier?: Identifier;
  /**
   * Allows a service to provide a unique, business identifier for the request.
   */
  identifier?: Identifier[];
  /**
   * A canonical URL referencing a FHIR-defined protocol, guideline, orderset or other definition that is adhered to in whole or in part by this request.
   */
  instantiatesCanonical?: string[];
  _instantiatesCanonical?: Element;
  /**
   * A URL referencing an externally defined protocol, guideline, orderset or other definition that is adhered to in whole or in part by this request.
   */
  instantiatesUri?: string[];
  _instantiatesUri?: Element;
  /**
   * Indicates the level of authority/intentionality associated with the request and where the request fits into the workflow chain.
   */
  intent: string;
  _intent?: Element;
  /**
   * Provides a mechanism to communicate additional information about the response.
   */
  note?: Annotation[];
  /**
   * Indicates how quickly the request should be addressed with respect to other requests.
   */
  priority?: string;
  _priority?: Element;
  /**
   * Describes the reason for the request group in coded or textual form.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Indicates another resource whose existence justifies this request group.
   */
  reasonReference?: Reference[];
  /**
   * The replacement could be because the initial request was immediately rejected (due to an issue) or because the previous request was completed, but the need for the action described by the request remains ongoing.
   */
  replaces?: Reference[];
  /**
   * The current state of the request. For request groups, the status reflects the status of all the requests in the group.
   */
  status: string;
  _status?: Element;
  /**
   * The subject for which the request group was created.
   */
  subject?: Reference;
}
/**
 * Code Values for the RequestGroup.intent field
 */
export enum RequestGroupIntentCodes {
  PROPOSAL = 'proposal',
  PLAN = 'plan',
  DIRECTIVE = 'directive',
  ORDER = 'order',
  ORIGINAL_ORDER = 'original-order',
  REFLEX_ORDER = 'reflex-order',
  FILLER_ORDER = 'filler-order',
  INSTANCE_ORDER = 'instance-order',
  OPTION = 'option',
}
/**
 * Code Values for the RequestGroup.priority field
 */
export enum RequestGroupPriorityCodes {
  ROUTINE = 'routine',
  URGENT = 'urgent',
  ASAP = 'asap',
  STAT = 'stat',
}
/**
 * Code Values for the RequestGroup.status field
 */
export enum RequestGroupStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  ON_HOLD = 'on-hold',
  REVOKED = 'revoked',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  UNKNOWN = 'unknown',
}
/**
 * The ResearchDefinition resource describes the conditional state (population and any exposures being compared within the population) and outcome (if specified) that the knowledge (evidence, assertion, recommendation) is about.
 */
export interface ResearchDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ResearchDefinition';
  /**
   * The 'date' element may be more recent than the approval date because of minor changes or editorial corrections.
   */
  approvalDate?: string;
  _approvalDate?: Element;
  /**
   * An individiual or organization primarily involved in the creation and maintenance of the content.
   */
  author?: ContactDetail[];
  /**
   * A human-readable string to clarify or explain concepts about the resource.
   */
  comment?: string[];
  _comment?: Element;
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the research definition and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the research definition.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the research definition. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the research definition was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the research definition as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the research definition is presumed to be the predominant language in the place the research definition was created).
   */
  description?: string;
  _description?: Element;
  /**
   * An individual or organization primarily responsible for internal coherence of the content.
   */
  editor?: ContactDetail[];
  /**
   * The effective period for a research definition  determines when the content is applicable for usage and is independent of publication and review dates. For example, a measure intended to be used for the year 2016 might be published in 2015.
   */
  effectivePeriod?: Period;
  /**
   * An individual or organization responsible for officially endorsing the content for use in some setting.
   */
  endorser?: ContactDetail[];
  /**
   * Allows filtering of research definitions that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * A reference to a ResearchElementDefinition resource that defines the exposure for the research.
   */
  exposure?: Reference;
  /**
   * A reference to a ResearchElementDefinition resource that defines the exposureAlternative for the research.
   */
  exposureAlternative?: Reference;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this research definition outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * It may be possible for the research definition to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * If specified, this date follows the original approval date.
   */
  lastReviewDate?: string;
  _lastReviewDate?: Element;
  /**
   * A reference to a Library resource containing the formal logic used by the ResearchDefinition.
   */
  library?: string[];
  _library?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * A reference to a ResearchElementDefinition resomece that defines the outcome for the research.
   */
  outcome?: Reference;
  /**
   * A reference to a ResearchElementDefinition resource that defines the population for the research.
   */
  population: Reference;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the research definition is the organization or individual primarily responsible for the maintenance and upkeep of the research definition. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the research definition. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the research definition. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this research definition.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Each related artifact is either an attachment, or a reference to another resource, but not both.
   */
  relatedArtifact?: RelatedArtifact[];
  /**
   * An individual or organization primarily responsible for review of some aspect of the content.
   */
  reviewer?: ContactDetail[];
  /**
   * The short title provides an alternate title for use in informal descriptive contexts where the full, formal title is not necessary.
   */
  shortTitle?: string;
  _shortTitle?: Element;
  /**
   * Allows filtering of research definitions that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * The subject of the ResearchDefinition is critical in interpreting the criteria definitions, as the logic in the ResearchDefinitions is evaluated with respect to a particular subject. This corresponds roughly to the notion of a Compartment in that it limits what content is available based on its relationship to the subject. In CQL, this corresponds to the context declaration.
   */
  subjectCodeableConcept?: CodeableConcept;
  /**
   * The subject of the ResearchDefinition is critical in interpreting the criteria definitions, as the logic in the ResearchDefinitions is evaluated with respect to a particular subject. This corresponds roughly to the notion of a Compartment in that it limits what content is available based on its relationship to the subject. In CQL, this corresponds to the context declaration.
   */
  subjectReference?: Reference;
  /**
   * An explanatory or alternate title for the ResearchDefinition giving additional information about its content.
   */
  subtitle?: string;
  _subtitle?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Descriptive topics related to the content of the ResearchDefinition. Topics provide a high-level categorization grouping types of ResearchDefinitions that can be useful for filtering and searching.
   */
  topic?: CodeableConcept[];
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * A detailed description, from a clinical perspective, of how the ResearchDefinition is used.
   */
  usage?: string;
  _usage?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different research definition instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the research definition with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the ResearchDefinition.status field
 */
export enum ResearchDefinitionStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Characteristics can be defined flexibly to accommodate different use cases for membership criteria, ranging from simple codes, all the way to using an expression language to express the criteria.
 */
export interface ResearchElementDefinitionCharacteristic extends BackboneElement {
  /**
   * Define members of the research element using Codes (such as condition, medication, or observation), Expressions ( using an expression language such as FHIRPath or CQL) or DataRequirements (such as Diabetes diagnosis onset in the last year).
   */
  definitionCodeableConcept: CodeableConcept;
  /**
   * Define members of the research element using Codes (such as condition, medication, or observation), Expressions ( using an expression language such as FHIRPath or CQL) or DataRequirements (such as Diabetes diagnosis onset in the last year).
   */
  definitionCanonical: string;
  _definitionCanonical?: Element;
  /**
   * Define members of the research element using Codes (such as condition, medication, or observation), Expressions ( using an expression language such as FHIRPath or CQL) or DataRequirements (such as Diabetes diagnosis onset in the last year).
   */
  definitionExpression: Expression;
  /**
   * Define members of the research element using Codes (such as condition, medication, or observation), Expressions ( using an expression language such as FHIRPath or CQL) or DataRequirements (such as Diabetes diagnosis onset in the last year).
   */
  definitionDataRequirement: DataRequirement;
  /**
   * When true, members with this characteristic are excluded from the element.
   */
  exclude?: boolean;
  _exclude?: Element;
  /**
   * Indicates what effective period the study covers.
   */
  participantEffectiveDateTime?: string;
  _participantEffectiveDateTime?: Element;
  /**
   * Indicates what effective period the study covers.
   */
  participantEffectivePeriod?: Period;
  /**
   * Indicates what effective period the study covers.
   */
  participantEffectiveDuration?: Duration;
  /**
   * Indicates what effective period the study covers.
   */
  participantEffectiveTiming?: Timing;
  /**
   * A narrative description of the time period the study covers.
   */
  participantEffectiveDescription?: string;
  _participantEffectiveDescription?: Element;
  /**
   * Indicates how elements are aggregated within the study effective period.
   */
  participantEffectiveGroupMeasure?: string;
  _participantEffectiveGroupMeasure?: Element;
  /**
   * Indicates duration from the participant's study entry.
   */
  participantEffectiveTimeFromStart?: Duration;
  /**
   * Indicates what effective period the study covers.
   */
  studyEffectiveDateTime?: string;
  _studyEffectiveDateTime?: Element;
  /**
   * Indicates what effective period the study covers.
   */
  studyEffectivePeriod?: Period;
  /**
   * Indicates what effective period the study covers.
   */
  studyEffectiveDuration?: Duration;
  /**
   * Indicates what effective period the study covers.
   */
  studyEffectiveTiming?: Timing;
  /**
   * A narrative description of the time period the study covers.
   */
  studyEffectiveDescription?: string;
  _studyEffectiveDescription?: Element;
  /**
   * Indicates how elements are aggregated within the study effective period.
   */
  studyEffectiveGroupMeasure?: string;
  _studyEffectiveGroupMeasure?: Element;
  /**
   * Indicates duration from the study initiation.
   */
  studyEffectiveTimeFromStart?: Duration;
  /**
   * Specifies the UCUM unit for the outcome.
   */
  unitOfMeasure?: CodeableConcept;
  /**
   * Use UsageContext to define the members of the population, such as Age Ranges, Genders, Settings.
   */
  usageContext?: UsageContext[];
}
/**
 * Code Values for the ResearchElementDefinition.characteristic.participantEffectiveGroupMeasure field
 */
export enum ResearchElementDefinitionCharacteristicParticipantEffectiveGroupMeasureCodes {
  MEAN = 'mean',
  MEDIAN = 'median',
  MEAN_OF_MEAN = 'mean-of-mean',
  MEAN_OF_MEDIAN = 'mean-of-median',
  MEDIAN_OF_MEAN = 'median-of-mean',
  MEDIAN_OF_MEDIAN = 'median-of-median',
}
/**
 * Code Values for the ResearchElementDefinition.characteristic.studyEffectiveGroupMeasure field
 */
export enum ResearchElementDefinitionCharacteristicStudyEffectiveGroupMeasureCodes {
  MEAN = 'mean',
  MEDIAN = 'median',
  MEAN_OF_MEAN = 'mean-of-mean',
  MEAN_OF_MEDIAN = 'mean-of-median',
  MEDIAN_OF_MEAN = 'median-of-mean',
  MEDIAN_OF_MEDIAN = 'median-of-median',
}
/**
 * The ResearchElementDefinition resource describes a "PICO" element that knowledge (evidence, assertion, recommendation) is about.
 */
export interface ResearchElementDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ResearchElementDefinition';
  /**
   * The 'date' element may be more recent than the approval date because of minor changes or editorial corrections.
   */
  approvalDate?: string;
  _approvalDate?: Element;
  /**
   * An individiual or organization primarily involved in the creation and maintenance of the content.
   */
  author?: ContactDetail[];
  /**
   * Characteristics can be defined flexibly to accommodate different use cases for membership criteria, ranging from simple codes, all the way to using an expression language to express the criteria.
   */
  characteristic: ResearchElementDefinitionCharacteristic[];
  /**
   * A human-readable string to clarify or explain concepts about the resource.
   */
  comment?: string[];
  _comment?: Element;
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the research element definition and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the research element definition.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the research element definition. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the research element definition was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the research element definition as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the research element definition is presumed to be the predominant language in the place the research element definition was created).
   */
  description?: string;
  _description?: Element;
  /**
   * An individual or organization primarily responsible for internal coherence of the content.
   */
  editor?: ContactDetail[];
  /**
   * The effective period for a research element definition  determines when the content is applicable for usage and is independent of publication and review dates. For example, a measure intended to be used for the year 2016 might be published in 2015.
   */
  effectivePeriod?: Period;
  /**
   * An individual or organization responsible for officially endorsing the content for use in some setting.
   */
  endorser?: ContactDetail[];
  /**
   * Allows filtering of research element definitions that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this research element definition outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * It may be possible for the research element definition to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * If specified, this date follows the original approval date.
   */
  lastReviewDate?: string;
  _lastReviewDate?: Element;
  /**
   * A reference to a Library resource containing the formal logic used by the ResearchElementDefinition.
   */
  library?: string[];
  _library?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the research element definition is the organization or individual primarily responsible for the maintenance and upkeep of the research element definition. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the research element definition. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the research element definition. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this research element definition.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Each related artifact is either an attachment, or a reference to another resource, but not both.
   */
  relatedArtifact?: RelatedArtifact[];
  /**
   * An individual or organization primarily responsible for review of some aspect of the content.
   */
  reviewer?: ContactDetail[];
  /**
   * The short title provides an alternate title for use in informal descriptive contexts where the full, formal title is not necessary.
   */
  shortTitle?: string;
  _shortTitle?: Element;
  /**
   * Allows filtering of research element definitions that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * The subject of the ResearchElementDefinition is critical in interpreting the criteria definitions, as the logic in the ResearchElementDefinitions is evaluated with respect to a particular subject. This corresponds roughly to the notion of a Compartment in that it limits what content is available based on its relationship to the subject. In CQL, this corresponds to the context declaration.
   */
  subjectCodeableConcept?: CodeableConcept;
  /**
   * The subject of the ResearchElementDefinition is critical in interpreting the criteria definitions, as the logic in the ResearchElementDefinitions is evaluated with respect to a particular subject. This corresponds roughly to the notion of a Compartment in that it limits what content is available based on its relationship to the subject. In CQL, this corresponds to the context declaration.
   */
  subjectReference?: Reference;
  /**
   * An explanatory or alternate title for the ResearchElementDefinition giving additional information about its content.
   */
  subtitle?: string;
  _subtitle?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Descriptive topics related to the content of the ResearchElementDefinition. Topics provide a high-level categorization grouping types of ResearchElementDefinitions that can be useful for filtering and searching.
   */
  topic?: CodeableConcept[];
  /**
   * The type of research element, a population, an exposure, or an outcome.
   */
  type: string;
  _type?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * A detailed description, from a clinical perspective, of how the ResearchElementDefinition is used.
   */
  usage?: string;
  _usage?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * The type of the outcome (e.g. Dichotomous, Continuous, or Descriptive).
   */
  variableType?: string;
  _variableType?: Element;
  /**
   * There may be different research element definition instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the research element definition with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the ResearchElementDefinition.status field
 */
export enum ResearchElementDefinitionStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Code Values for the ResearchElementDefinition.type field
 */
export enum ResearchElementDefinitionTypeCodes {
  POPULATION = 'population',
  EXPOSURE = 'exposure',
  OUTCOME = 'outcome',
}
/**
 * Code Values for the ResearchElementDefinition.variableType field
 */
export enum ResearchElementDefinitionVariableTypeCodes {
  DICHOTOMOUS = 'dichotomous',
  CONTINUOUS = 'continuous',
  DESCRIPTIVE = 'descriptive',
}
/**
 * Describes an expected sequence of events for one of the participants of a study.  E.g. Exposure to drug A, wash-out, exposure to drug B, wash-out, follow-up.
 */
export interface ResearchStudyArm extends BackboneElement {
  /**
   * A succinct description of the path through the study that would be followed by a subject adhering to this arm.
   */
  description?: string;
  _description?: Element;
  /**
   * Unique, human-readable label for this arm of the study.
   */
  name: string;
  _name?: Element;
  /**
   * Categorization of study arm, e.g. experimental, active comparator, placebo comparater.
   */
  type?: CodeableConcept;
}
/**
 * A goal that the study is aiming to achieve in terms of a scientific question to be answered by the analysis of data collected during the study.
 */
export interface ResearchStudyObjective extends BackboneElement {
  /**
   * Unique, human-readable label for this objective of the study.
   */
  name?: string;
  _name?: Element;
  /**
   * The kind of study objective.
   */
  type?: CodeableConcept;
}
/**
 * A process where a researcher or organization plans and then executes a series of steps intended to increase the field of healthcare-related knowledge.  This includes studies of safety, efficacy, comparative effectiveness and other information about medications, devices, therapies and other interventional and investigative techniques.  A ResearchStudy involves the gathering of information about human or animal subjects.
 */
export interface ResearchStudy extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ResearchStudy';
  /**
   * Describes an expected sequence of events for one of the participants of a study.  E.g. Exposure to drug A, wash-out, exposure to drug B, wash-out, follow-up.
   */
  arm?: ResearchStudyArm[];
  /**
   * Codes categorizing the type of study such as investigational vs. observational, type of blinding, type of randomization, safety vs. efficacy, etc.
   */
  category?: CodeableConcept[];
  /**
   * The condition that is the focus of the study.  For example, In a study to examine risk factors for Lupus, might have as an inclusion criterion "healthy volunteer", but the target condition code would be a Lupus SNOMED code.
   */
  condition?: CodeableConcept[];
  /**
   * Contact details to assist a user in learning more about or engaging with the study.
   */
  contact?: ContactDetail[];
  /**
   * A full description of how the study is being conducted.
   */
  description?: string;
  _description?: Element;
  /**
   * The Group referenced should not generally enumerate specific subjects.  Subjects will be linked to the study using the ResearchSubject resource.
   */
  enrollment?: Reference[];
  /**
   * The medication(s), food(s), therapy(ies), device(s) or other concerns or interventions that the study is seeking to gain more information about.
   */
  focus?: CodeableConcept[];
  /**
   * Identifiers assigned to this research study by the sponsor or other systems.
   */
  identifier?: Identifier[];
  /**
   * Key terms to aid in searching for or filtering the study.
   */
  keyword?: CodeableConcept[];
  /**
   * Indicates a country, state or other region where the study is taking place.
   */
  location?: CodeableConcept[];
  /**
   * Comments made about the study by the performer, subject or other participants.
   */
  note?: Annotation[];
  /**
   * A goal that the study is aiming to achieve in terms of a scientific question to be answered by the analysis of data collected during the study.
   */
  objective?: ResearchStudyObjective[];
  /**
   * A larger research study of which this particular study is a component or step.
   */
  partOf?: Reference[];
  /**
   * Identifies the start date and the expected (or actual, depending on status) end date for the study.
   */
  period?: Period;
  /**
   * The stage in the progression of a therapy from initial experimental use in humans in clinical trials to post-market evaluation.
   */
  phase?: CodeableConcept;
  /**
   * The type of study based upon the intent of the study's activities. A classification of the intent of the study.
   */
  primaryPurposeType?: CodeableConcept;
  /**
   * A researcher in a study who oversees multiple aspects of the study, such as concept development, protocol writing, protocol submission for IRB approval, participant recruitment, informed consent, data collection, analysis, interpretation and presentation.
   */
  principalInvestigator?: Reference;
  /**
   * The set of steps expected to be performed as part of the execution of the study.
   */
  protocol?: Reference[];
  /**
   * A description and/or code explaining the premature termination of the study.
   */
  reasonStopped?: CodeableConcept;
  /**
   * Citations, references and other related documents.
   */
  relatedArtifact?: RelatedArtifact[];
  /**
   * A facility in which study activities are conducted.
   */
  site?: Reference[];
  /**
   * An organization that initiates the investigation and is legally responsible for the study.
   */
  sponsor?: Reference;
  /**
   * The current state of the study.
   */
  status: string;
  _status?: Element;
  /**
   * A short, descriptive user-friendly label for the study.
   */
  title?: string;
  _title?: Element;
}
/**
 * Code Values for the ResearchStudy.status field
 */
export enum ResearchStudyStatusCodes {
  ACTIVE = 'active',
  ADMINISTRATIVELY_COMPLETED = 'administratively-completed',
  APPROVED = 'approved',
  CLOSED_TO_ACCRUAL = 'closed-to-accrual',
  CLOSED_TO_ACCRUAL_AND_INTERVENTION = 'closed-to-accrual-and-intervention',
  COMPLETED = 'completed',
  DISAPPROVED = 'disapproved',
  IN_REVIEW = 'in-review',
  TEMPORARILY_CLOSED_TO_ACCRUAL = 'temporarily-closed-to-accrual',
  TEMPORARILY_CLOSED_TO_ACCRUAL_AND_INTERVENTION = 'temporarily-closed-to-accrual-and-intervention',
  WITHDRAWN = 'withdrawn',
}
/**
 * A physical entity which is the primary unit of operational and/or administrative interest in a study.
 */
export interface ResearchSubject extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ResearchSubject';
  /**
   * The name of the arm in the study the subject actually followed as part of this study.
   */
  actualArm?: string;
  _actualArm?: Element;
  /**
   * The name of the arm in the study the subject is expected to follow as part of this study.
   */
  assignedArm?: string;
  _assignedArm?: Element;
  /**
   * A record of the patient's informed agreement to participate in the study.
   */
  consent?: Reference;
  /**
   * Identifiers assigned to this research subject for a study.
   */
  identifier?: Identifier[];
  /**
   * The record of the person or animal who is involved in the study.
   */
  individual: Reference;
  /**
   * The dates the subject began and ended their participation in the study.
   */
  period?: Period;
  /**
   * The current state of the subject.
   */
  status: string;
  _status?: Element;
  /**
   * Reference to the study the subject is participating in.
   */
  study: Reference;
}
/**
 * Code Values for the ResearchSubject.status field
 */
export enum ResearchSubjectStatusCodes {
  CANDIDATE = 'candidate',
  ELIGIBLE = 'eligible',
  FOLLOW_UP = 'follow-up',
  INELIGIBLE = 'ineligible',
  NOT_REGISTERED = 'not-registered',
  OFF_STUDY = 'off-study',
  ON_STUDY = 'on-study',
  ON_STUDY_INTERVENTION = 'on-study-intervention',
  ON_STUDY_OBSERVATION = 'on-study-observation',
  PENDING_ON_STUDY = 'pending-on-study',
  POTENTIAL_CANDIDATE = 'potential-candidate',
  SCREENING = 'screening',
  WITHDRAWN = 'withdrawn',
}
/**
 * This is the base resource type for everything.
 */
export interface Resource {
  /**
   * The only time that a resource does not have an id is when it is being submitted to the server using a create operation.
   */
  id?: string;
  _id?: Element;
  /**
   * Asserting this rule set restricts the content to be only understood by a limited set of trading partners. This inherently limits the usefulness of the data in the long term. However, the existing health eco-system is highly fractured, and not yet ready to define, collect, and exchange data in a generally computable sense. Wherever possible, implementers and/or specification writers should avoid using this element. Often, when used, the URL is a reference to an implementation guide that defines these special rules as part of it's narrative along with other profiles, value sets, etc.
   */
  implicitRules?: string;
  _implicitRules?: Element;
  /**
   * Language is provided to support indexing and accessibility (typically, services such as text to speech use the language tag). The html language tag in the narrative applies  to the narrative. The language tag on the resource may be used to specify the language of other presentations generated from the data in the resource. Not all the content has to be in the base language. The Resource.language should not be assumed to apply to the narrative automatically. If a language is specified, it should it also be specified on the div element in the html (see rules in HTML5 for information about the relationship between xml:lang and the html lang attribute).
   */
  language?: string;
  _language?: Element;
  /**
   * The metadata about the resource. This is content that is maintained by the infrastructure. Changes to the content might not always be associated with version changes to the resource.
   */
  meta?: Meta;
}
/**
 * Multiple repetitions can be used to identify the same type of outcome in different timeframes as well as different types of outcomes.
 */
export interface RiskAssessmentPrediction extends BackboneElement {
  /**
   * One of the potential outcomes for the patient (e.g. remission, death,  a particular condition).
   */
  outcome?: CodeableConcept;
  /**
   * If range is used, it represents the lower and upper bounds of certainty; e.g. 40-60%  Decimal values are expressed as percentages as well (max = 100).
   */
  probabilityDecimal?: number;
  /**
   * If range is used, it represents the lower and upper bounds of certainty; e.g. 40-60%  Decimal values are expressed as percentages as well (max = 100).
   */
  probabilityRange?: Range;
  /**
   * Indicates how likely the outcome is (in the specified timeframe), expressed as a qualitative value (e.g. low, medium, or high).
   */
  qualitativeRisk?: CodeableConcept;
  /**
   * Additional information explaining the basis for the prediction.
   */
  rationale?: string;
  _rationale?: Element;
  /**
   * Indicates the risk for this particular subject (with their specific characteristics) divided by the risk of the population in general.  (Numbers greater than 1 = higher risk than the population, numbers less than 1 = lower risk.).
   */
  relativeRisk?: number;
  /**
   * If not specified, the risk applies "over the subject's lifespan".
   */
  whenPeriod?: Period;
  /**
   * If not specified, the risk applies "over the subject's lifespan".
   */
  whenRange?: Range;
}
/**
 * An assessment of the likely outcome(s) for a patient or other subject as well as the likelihood of each outcome.
 */
export interface RiskAssessment extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'RiskAssessment';
  /**
   * A reference to the request that is fulfilled by this risk assessment.
   */
  basedOn?: Reference;
  /**
   * Indicates the source data considered as part of the assessment (for example, FamilyHistory, Observations, Procedures, Conditions, etc.).
   */
  basis?: Reference[];
  /**
   * The type of the risk assessment performed.
   */
  code?: CodeableConcept;
  /**
   * For assessments or prognosis specific to a particular condition, indicates the condition being assessed.
   */
  condition?: Reference;
  /**
   * The encounter where the assessment was performed.
   */
  encounter?: Reference;
  /**
   * Business identifier assigned to the risk assessment.
   */
  identifier?: Identifier[];
  /**
   * The algorithm, process or mechanism used to evaluate the risk.
   */
  method?: CodeableConcept;
  /**
   * A description of the steps that might be taken to reduce the identified risk(s).
   */
  mitigation?: string;
  _mitigation?: Element;
  /**
   * Additional comments about the risk assessment.
   */
  note?: Annotation[];
  /**
   * The date (and possibly time) the risk assessment was performed.
   */
  occurrenceDateTime?: string;
  _occurrenceDateTime?: Element;
  /**
   * The date (and possibly time) the risk assessment was performed.
   */
  occurrencePeriod?: Period;
  /**
   * A reference to a resource that this risk assessment is part of, such as a Procedure.
   */
  parent?: Reference;
  /**
   * The provider or software application that performed the assessment.
   */
  performer?: Reference;
  /**
   * Multiple repetitions can be used to identify the same type of outcome in different timeframes as well as different types of outcomes.
   */
  prediction?: RiskAssessmentPrediction[];
  /**
   * The reason the risk assessment was performed.
   */
  reasonCode?: CodeableConcept[];
  /**
   * Resources supporting the reason the risk assessment was performed.
   */
  reasonReference?: Reference[];
  /**
   * The status of the RiskAssessment, using the same statuses as an Observation.
   */
  status: string;
  _status?: Element;
  /**
   * The patient or group the risk assessment applies to.
   */
  subject: Reference;
}
/**
 * Code Values for the RiskAssessment.status field
 */
export enum RiskAssessmentStatusCodes {
  REGISTERED = 'registered',
  PRELIMINARY = 'preliminary',
  FINAL = 'final',
  AMENDED = 'amended',
}
/**
 * A description of the size of the sample involved in the synthesis.
 */
export interface RiskEvidenceSynthesisSampleSize extends BackboneElement {
  /**
   * Human-readable summary of sample size.
   */
  description?: string;
  _description?: Element;
  /**
   * Number of participants included in this evidence synthesis.
   */
  numberOfParticipants?: number;
  /**
   * Number of studies included in this evidence synthesis.
   */
  numberOfStudies?: number;
}
/**
 * A description of the precision of the estimate for the effect.
 */
export interface RiskEvidenceSynthesisRiskEstimatePrecisionEstimate extends BackboneElement {
  /**
   * Lower bound of confidence interval.
   */
  from?: number;
  /**
   * Use 95 for a 95% confidence interval.
   */
  level?: number;
  /**
   * Upper bound of confidence interval.
   */
  to?: number;
  /**
   * Examples include confidence interval and interquartile range.
   */
  type?: CodeableConcept;
}
/**
 * The estimated risk of the outcome.
 */
export interface RiskEvidenceSynthesisRiskEstimate extends BackboneElement {
  /**
   * The sample size for the group that was measured for this risk estimate.
   */
  denominatorCount?: number;
  /**
   * Human-readable summary of risk estimate.
   */
  description?: string;
  _description?: Element;
  /**
   * The number of group members with the outcome of interest.
   */
  numeratorCount?: number;
  /**
   * A description of the precision of the estimate for the effect.
   */
  precisionEstimate?: RiskEvidenceSynthesisRiskEstimatePrecisionEstimate[];
  /**
   * Examples include proportion and mean.
   */
  type?: CodeableConcept;
  /**
   * Specifies the UCUM unit for the outcome.
   */
  unitOfMeasure?: CodeableConcept;
  /**
   * The point estimate of the risk estimate.
   */
  value?: number;
}
/**
 * A description of a component of the overall certainty.
 */
export interface RiskEvidenceSynthesisCertaintyCertaintySubcomponent extends BackboneElement {
  /**
   * A human-readable string to clarify or explain concepts about the resource.
   */
  note?: Annotation[];
  /**
   * A rating of a subcomponent of rating certainty.
   */
  rating?: CodeableConcept[];
  /**
   * Type of subcomponent of certainty rating.
   */
  type?: CodeableConcept;
}
/**
 * A description of the certainty of the risk estimate.
 */
export interface RiskEvidenceSynthesisCertainty extends BackboneElement {
  /**
   * A description of a component of the overall certainty.
   */
  certaintySubcomponent?: RiskEvidenceSynthesisCertaintyCertaintySubcomponent[];
  /**
   * A human-readable string to clarify or explain concepts about the resource.
   */
  note?: Annotation[];
  /**
   * A rating of the certainty of the effect estimate.
   */
  rating?: CodeableConcept[];
}
/**
 * The RiskEvidenceSynthesis resource describes the likelihood of an outcome in a population plus exposure state where the risk estimate is derived from a combination of research studies.
 */
export interface RiskEvidenceSynthesis extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'RiskEvidenceSynthesis';
  /**
   * The 'date' element may be more recent than the approval date because of minor changes or editorial corrections.
   */
  approvalDate?: string;
  _approvalDate?: Element;
  /**
   * An individiual or organization primarily involved in the creation and maintenance of the content.
   */
  author?: ContactDetail[];
  /**
   * A description of the certainty of the risk estimate.
   */
  certainty?: RiskEvidenceSynthesisCertainty[];
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the risk evidence synthesis and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the risk evidence synthesis.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the risk evidence synthesis. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the risk evidence synthesis was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the risk evidence synthesis as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the risk evidence synthesis is presumed to be the predominant language in the place the risk evidence synthesis was created).
   */
  description?: string;
  _description?: Element;
  /**
   * An individual or organization primarily responsible for internal coherence of the content.
   */
  editor?: ContactDetail[];
  /**
   * The effective period for a risk evidence synthesis  determines when the content is applicable for usage and is independent of publication and review dates. For example, a measure intended to be used for the year 2016 might be published in 2015.
   */
  effectivePeriod?: Period;
  /**
   * An individual or organization responsible for officially endorsing the content for use in some setting.
   */
  endorser?: ContactDetail[];
  /**
   * A reference to a EvidenceVariable resource that defines the exposure for the research.
   */
  exposure?: Reference;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this risk evidence synthesis outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * It may be possible for the risk evidence synthesis to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * If specified, this date follows the original approval date.
   */
  lastReviewDate?: string;
  _lastReviewDate?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * A human-readable string to clarify or explain concepts about the resource.
   */
  note?: Annotation[];
  /**
   * A reference to a EvidenceVariable resomece that defines the outcome for the research.
   */
  outcome: Reference;
  /**
   * A reference to a EvidenceVariable resource that defines the population for the research.
   */
  population: Reference;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the risk evidence synthesis is the organization or individual primarily responsible for the maintenance and upkeep of the risk evidence synthesis. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the risk evidence synthesis. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * Each related artifact is either an attachment, or a reference to another resource, but not both.
   */
  relatedArtifact?: RelatedArtifact[];
  /**
   * An individual or organization primarily responsible for review of some aspect of the content.
   */
  reviewer?: ContactDetail[];
  /**
   * The estimated risk of the outcome.
   */
  riskEstimate?: RiskEvidenceSynthesisRiskEstimate;
  /**
   * A description of the size of the sample involved in the synthesis.
   */
  sampleSize?: RiskEvidenceSynthesisSampleSize;
  /**
   * Allows filtering of risk evidence synthesiss that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * Type of study eg randomized trial.
   */
  studyType?: CodeableConcept;
  /**
   * Type of synthesis eg meta-analysis.
   */
  synthesisType?: CodeableConcept;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Descriptive topics related to the content of the RiskEvidenceSynthesis. Topics provide a high-level categorization grouping types of EffectEvidenceSynthesiss that can be useful for filtering and searching.
   */
  topic?: CodeableConcept[];
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different risk evidence synthesis instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the risk evidence synthesis with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the RiskEvidenceSynthesis.status field
 */
export enum RiskEvidenceSynthesisStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * A container for slots of time that may be available for booking appointments.
 */
export interface Schedule extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Schedule';
  /**
   * This element is labeled as a modifier because it may be used to mark that the resource was created in error.
   */
  active?: boolean;
  _active?: Element;
  /**
   * The capacity to support multiple referenced resource types should be used in cases where the specific resources themselves cannot be scheduled without the other, and thus only make sense to the system exposing them as a group. Common examples of this are where the combination of a practitioner and a room (Location) are always required by a system.
   */
  actor: Reference[];
  /**
   * Comments on the availability to describe any extended information. Such as custom constraints on the slots that may be associated.
   */
  comment?: string;
  _comment?: Element;
  /**
   * External Ids for this item.
   */
  identifier?: Identifier[];
  /**
   * The period of time that the slots that reference this Schedule resource cover (even if none exist). These  cover the amount of time that an organization's planning horizon; the interval for which they are currently accepting appointments. This does not define a "template" for planning outside these dates.
   */
  planningHorizon?: Period;
  /**
   * A broad categorization of the service that is to be performed during this appointment.
   */
  serviceCategory?: CodeableConcept[];
  /**
   * The specific service that is to be performed during this appointment.
   */
  serviceType?: CodeableConcept[];
  /**
   * The specialty of a practitioner that would be required to perform the service requested in this appointment.
   */
  specialty?: CodeableConcept[];
}
/**
 * Used to define the parts of a composite search parameter.
 */
export interface SearchParameterComponent extends BackboneElement {
  /**
   * The definition of the search parameter that describes this part.
   */
  definition: string;
  _definition?: Element;
  /**
   * This expression overrides the expression in the definition and extracts the index values from the outcome of the composite expression.
   */
  expression: string;
  _expression?: Element;
}
/**
 * A search parameter that defines a named search item that can be used to search/filter on a resource.
 */
export interface SearchParameter extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'SearchParameter';
  /**
   * A search parameter must always apply to at least one resource type. When search parameters apply to more than one resource type, they can be used against any of the listed resource types, or in a cross-type search (see [Cross Resource Search](http.html#xres-search)).
   */
  base: string[];
  _base?: Element;
  /**
   * Systems are not required to list all the chain names they support, but if they don't list them, clients might not know to use them.
   */
  chain?: string[];
  _chain?: Element;
  /**
   * For maximum compatibility, use only lowercase ASCII characters.
   */
  code: string;
  _code?: Element;
  /**
   * If no comparators are listed, clients should not expect servers to support any comparators.
   */
  comparator?: string[];
  _comparator?: Element;
  /**
   * Used to define the parts of a composite search parameter.
   */
  component?: SearchParameterComponent[];
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the search parameter. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * The intent of this is that a server can designate that it provides support for a search parameter defined in the specification itself (e.g.  [`value-quantity`](http://hl7.org/fhir/SearchParameter/Observation-value-quantity), but detail how it is supported by the server.
   */
  derivedFrom?: string;
  _derivedFrom?: Element;
  /**
   * This description can be used to capture details such as why the search parameter was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the search parameter as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the search parameter is presumed to be the predominant language in the place the search parameter was created).
   */
  description: string;
  _description?: Element;
  /**
   * Allows filtering of search parameters that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Note that the elements returned by the expression are sometimes complex elements where logic is required to determine quite how to handle them; e.g. CodeableConcepts may contain text and/or multiple codings, where the codings themselves contain a code and a system. For composite search parameters, the outcome of the expression must a collection of base elements from which the composites are derived.
   */
  expression?: string;
  _expression?: Element;
  /**
   * It may be possible for the search parameter to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * A modifier supported for the search parameter.
   */
  modifier?: string[];
  _modifier?: Element;
  /**
   * Whether multiple parameters are allowed - e.g. more than one parameter with the same name. The search matches if all the parameters match.
   */
  multipleAnd?: boolean;
  _multipleAnd?: Element;
  /**
   * Whether multiple values are allowed for each time the parameter exists. Values are separated by commas, and the parameter matches if any of the values match.
   */
  multipleOr?: boolean;
  _multipleOr?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name: string;
  _name?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the search parameter is the organization or individual primarily responsible for the maintenance and upkeep of the search parameter. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the search parameter. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the search parameter. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this search parameter.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Allows filtering of search parameters that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * Types of resource (if a resource is referenced).
   */
  target?: string[];
  _target?: Element;
  /**
   * The type of value that a search parameter may contain, and how the content is interpreted.
   */
  type: string;
  _type?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different search parameter instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the search parameter with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
  /**
   * Note that the elements returned by the XPath are sometimes complex elements where logic is required to determine quite how to handle them; e.g. CodeableConcepts may contain text and/or multiple codings, where the codings themselves contain a code and a system.
   */
  xpath?: string;
  _xpath?: Element;
  /**
   * How the search parameter relates to the set of elements returned by evaluating the xpath query.
   */
  xpathUsage?: string;
  _xpathUsage?: Element;
}
/**
 * Code Values for the SearchParameter.comparator field
 */
export enum SearchParameterComparatorCodes {
  EQ = 'eq',
  NE = 'ne',
  GT = 'gt',
  LT = 'lt',
  GE = 'ge',
  LE = 'le',
  SA = 'sa',
  EB = 'eb',
  AP = 'ap',
}
/**
 * Code Values for the SearchParameter.modifier field
 */
export enum SearchParameterModifierCodes {
  MISSING = 'missing',
  EXACT = 'exact',
  CONTAINS = 'contains',
  NOT = 'not',
  TEXT = 'text',
  IN = 'in',
  NOT_IN = 'not-in',
  BELOW = 'below',
  ABOVE = 'above',
  TYPE = 'type',
  IDENTIFIER = 'identifier',
  OFTYPE = 'ofType',
}
/**
 * Code Values for the SearchParameter.status field
 */
export enum SearchParameterStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Code Values for the SearchParameter.type field
 */
export enum SearchParameterTypeCodes {
  NUMBER = 'number',
  DATE = 'date',
  STRING = 'string',
  TOKEN = 'token',
  REFERENCE = 'reference',
  COMPOSITE = 'composite',
  QUANTITY = 'quantity',
  URI = 'uri',
  SPECIAL = 'special',
}
/**
 * Code Values for the SearchParameter.xpathUsage field
 */
export enum SearchParameterXpathUsageCodes {
  NORMAL = 'normal',
  PHONETIC = 'phonetic',
  NEARBY = 'nearby',
  DISTANCE = 'distance',
  OTHER = 'other',
}
/**
 * A record of a request for service such as diagnostic investigations, treatments, or operations to be performed.
 */
export interface ServiceRequest extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ServiceRequest';
  /**
   * If a CodeableConcept is present, it indicates the pre-condition for performing the service.  For example "pain", "on flare-up", etc.
   */
  asNeededBoolean?: boolean;
  _asNeededBoolean?: Element;
  /**
   * If a CodeableConcept is present, it indicates the pre-condition for performing the service.  For example "pain", "on flare-up", etc.
   */
  asNeededCodeableConcept?: CodeableConcept;
  /**
   * When the request transitioned to being actionable.
   */
  authoredOn?: string;
  _authoredOn?: Element;
  /**
   * Plan/proposal/order fulfilled by this request.
   */
  basedOn?: Reference[];
  /**
   * Only used if not implicit in the code found in ServiceRequest.code.  If the use case requires BodySite to be handled as a separate resource instead of an inline coded element (e.g. to identify and track separately)  then use the standard extension [procedure-targetBodyStructure](extension-procedure-targetbodystructure.html).
   */
  bodySite?: CodeableConcept[];
  /**
   * There may be multiple axis of categorization depending on the context or use case for retrieving or displaying the resource.  The level of granularity is defined by the category concepts in the value set.
   */
  category?: CodeableConcept[];
  /**
   * Many laboratory and radiology procedure codes embed the specimen/organ system in the test order name, for example,  serum or serum/plasma glucose, or a chest x-ray. The specimen might not be recorded separately from the test code.
   */
  code?: CodeableConcept;
  /**
   * In general, only the code and timeframe will be present, though occasional additional qualifiers such as body site or even performer could be included to narrow the scope of the prohibition.  If the ServiceRequest.code and ServiceRequest.doNotPerform both contain negation, that will reinforce prohibition and should not have a double negative interpretation.
   */
  doNotPerform?: boolean;
  _doNotPerform?: Element;
  /**
   * An encounter that provides additional information about the healthcare context in which this request is made.
   */
  encounter?: Reference;
  /**
   * The identifier.type element is used to distinguish between the identifiers assigned by the orderer (known as the 'Placer' in HL7 v2) and the producer of the observations in response to the order (known as the 'Filler' in HL7 v2).  For further discussion and examples see the resource notes section below.
   */
  identifier?: Identifier[];
  /**
   * Note: This is a business identifier, not a resource identifier (see [discussion](resource.html#identifiers)).  It is best practice for the identifier to only appear on a single resource instance, however business practices may occasionally dictate that multiple resource instances with the same identifier can exist - possibly even with different resource types.  For example, multiple Patient and a Person resource instance might share the same social insurance number.
   */
  instantiatesCanonical?: string[];
  _instantiatesCanonical?: Element;
  /**
   * This might be an HTML page, PDF, etc. or could just be a non-resolvable URI identifier.
   */
  instantiatesUri?: string[];
  _instantiatesUri?: Element;
  /**
   * Insurance plans, coverage extensions, pre-authorizations and/or pre-determinations that may be needed for delivering the requested service.
   */
  insurance?: Reference[];
  /**
   * This element is labeled as a modifier because the intent alters when and how the resource is actually applicable.
   */
  intent: string;
  _intent?: Element;
  /**
   * The preferred location(s) where the procedure should actually happen in coded or free text form. E.g. at home or nursing day care center.
   */
  locationCode?: CodeableConcept[];
  /**
   * A reference to the the preferred location(s) where the procedure should actually happen. E.g. at home or nursing day care center.
   */
  locationReference?: Reference[];
  /**
   * Any other notes and comments made about the service request. For example, internal billing notes.
   */
  note?: Annotation[];
  /**
   * The date/time at which the requested service should occur.
   */
  occurrenceDateTime?: string;
  _occurrenceDateTime?: Element;
  /**
   * The date/time at which the requested service should occur.
   */
  occurrencePeriod?: Period;
  /**
   * The date/time at which the requested service should occur.
   */
  occurrenceTiming?: Timing;
  /**
   * For information from the medical record intended to support the delivery of the requested services, use the `supportingInformation` element.
   */
  orderDetail?: CodeableConcept[];
  /**
   * Instructions in terms that are understood by the patient or consumer.
   */
  patientInstruction?: string;
  _patientInstruction?: Element;
  /**
   * If multiple performers are present, it is interpreted as a list of *alternative* performers without any preference regardless of order.  If order of preference is needed use the [request-performerOrder extension](extension-request-performerorder.html).  Use CareTeam to represent a group of performers (for example, Practitioner A *and* Practitioner B).
   */
  performer?: Reference[];
  /**
   * This is a  role, not  a participation type.  In other words, does not describe the task but describes the capacity.  For example, “compounding pharmacy”, “psychiatrist” or “internal referral”.
   */
  performerType?: CodeableConcept;
  /**
   * Indicates how quickly the ServiceRequest should be addressed with respect to other requests.
   */
  priority?: string;
  _priority?: Element;
  /**
   * An amount of service being requested which can be a quantity ( for example $1,500 home modification), a ratio ( for example, 20 half day visits per month), or a range (2.0 to 1.8 Gy per fraction).
   */
  quantityQuantity?: Quantity;
  /**
   * An amount of service being requested which can be a quantity ( for example $1,500 home modification), a ratio ( for example, 20 half day visits per month), or a range (2.0 to 1.8 Gy per fraction).
   */
  quantityRatio?: Ratio;
  /**
   * An amount of service being requested which can be a quantity ( for example $1,500 home modification), a ratio ( for example, 20 half day visits per month), or a range (2.0 to 1.8 Gy per fraction).
   */
  quantityRange?: Range;
  /**
   * This element represents why the referral is being made and may be used to decide how the service will be performed, or even if it will be performed at all.   Use `CodeableConcept.text` element if the data is free (uncoded) text as shown in the [CT Scan example](servicerequest-example-di.html).
   */
  reasonCode?: CodeableConcept[];
  /**
   * This element represents why the referral is being made and may be used to decide how the service will be performed, or even if it will be performed at all.    To be as specific as possible,  a reference to  *Observation* or *Condition* should be used if available.  Otherwise when referencing  *DiagnosticReport*  it should contain a finding  in `DiagnosticReport.conclusion` and/or `DiagnosticReport.conclusionCode`.   When using a reference to *DocumentReference*, the target document should contain clear findings language providing the relevant reason for this service request.  Use  the CodeableConcept text element in `ServiceRequest.reasonCode` if the data is free (uncoded) text as shown in the [CT Scan example](servicerequest-example-di.html).
   */
  reasonReference?: Reference[];
  /**
   * This might not include provenances for all versions of the request – only those deemed “relevant” or important.
   * This SHALL NOT include the Provenance associated with this current version of the resource.  (If that provenance is deemed to be a “relevant” change, it will need to be added as part of a later update.  Until then, it can be queried directly as the Provenance that points to this version using _revinclude
   * All Provenances should have some historical version of this Request as their subject.
   */
  relevantHistory?: Reference[];
  /**
   * The request takes the place of the referenced completed or terminated request(s).
   */
  replaces?: Reference[];
  /**
   * This not the dispatcher, but rather who is the authorizer.  This element is not intended to handle delegation which would generally be managed through the Provenance resource.
   */
  requester?: Reference;
  /**
   * Requests are linked either by a "basedOn" relationship (i.e. one request is fulfilling another) or by having a common requisition. Requests that are part of the same requisition are generally treated independently from the perspective of changing their state or maintaining them after initial creation.
   */
  requisition?: Identifier;
  /**
   * Many diagnostic procedures need a specimen, but the request itself is not actually about the specimen. This element is for when the diagnostic is requested on already existing specimens and the request points to the specimen it applies to.    Conversely, if the request is entered first with an unknown specimen, then the [Specimen](specimen.html) resource points to the ServiceRequest.
   */
  specimen?: Reference[];
  /**
   * The status is generally fully in the control of the requester - they determine whether the order is draft or active and, after it has been activated, competed, cancelled or suspended. States relating to the activities of the performer are reflected on either the corresponding event (see [Event Pattern](event.html) for general discussion) or using the [Task](task.html) resource.
   */
  status: string;
  _status?: Element;
  /**
   * On whom or what the service is to be performed. This is usually a human patient, but can also be requested on animals, groups of humans or animals, devices such as dialysis machines, or even locations (typically for environmental scans).
   */
  subject: Reference;
  /**
   * To represent information about how the services are to be delivered use the `instructions` element.
   */
  supportingInfo?: Reference[];
}
/**
 * Code Values for the ServiceRequest.intent field
 */
export enum ServiceRequestIntentCodes {
  PROPOSAL = 'proposal',
  PLAN = 'plan',
  DIRECTIVE = 'directive',
  ORDER = 'order',
  ORIGINAL_ORDER = 'original-order',
  REFLEX_ORDER = 'reflex-order',
  FILLER_ORDER = 'filler-order',
  INSTANCE_ORDER = 'instance-order',
  OPTION = 'option',
}
/**
 * Code Values for the ServiceRequest.priority field
 */
export enum ServiceRequestPriorityCodes {
  ROUTINE = 'routine',
  URGENT = 'urgent',
  ASAP = 'asap',
  STAT = 'stat',
}
/**
 * Code Values for the ServiceRequest.status field
 */
export enum ServiceRequestStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  ON_HOLD = 'on-hold',
  REVOKED = 'revoked',
  COMPLETED = 'completed',
  ENTERED_IN_ERROR = 'entered-in-error',
  UNKNOWN = 'unknown',
}
/**
 * A slot of time on a schedule that may be available for booking appointments.
 */
export interface Slot extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Slot';
  /**
   * The style of appointment or patient that may be booked in the slot (not service type).
   */
  appointmentType?: CodeableConcept;
  /**
   * Comments on the slot to describe any extended information. Such as custom constraints on the slot.
   */
  comment?: string;
  _comment?: Element;
  /**
   * Date/Time that the slot is to conclude.
   */
  end: string;
  _end?: Element;
  /**
   * External Ids for this item.
   */
  identifier?: Identifier[];
  /**
   * This slot has already been overbooked, appointments are unlikely to be accepted for this time.
   */
  overbooked?: boolean;
  _overbooked?: Element;
  /**
   * The schedule resource that this slot defines an interval of status information.
   */
  schedule: Reference;
  /**
   * A broad categorization of the service that is to be performed during this appointment.
   */
  serviceCategory?: CodeableConcept[];
  /**
   * The type of appointments that can be booked into this slot (ideally this would be an identifiable service - which is at a location, rather than the location itself). If provided then this overrides the value provided on the availability resource.
   */
  serviceType?: CodeableConcept[];
  /**
   * The specialty of a practitioner that would be required to perform the service requested in this appointment.
   */
  specialty?: CodeableConcept[];
  /**
   * Date/Time that the slot is to begin.
   */
  start: string;
  _start?: Element;
  /**
   * busy | free | busy-unavailable | busy-tentative | entered-in-error.
   */
  status: string;
  _status?: Element;
}
/**
 * Code Values for the Slot.status field
 */
export enum SlotStatusCodes {
  BUSY = 'busy',
  FREE = 'free',
  BUSY_UNAVAILABLE = 'busy-unavailable',
  BUSY_TENTATIVE = 'busy-tentative',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Details concerning the specimen collection.
 */
export interface SpecimenCollection extends BackboneElement {
  /**
   * If the use case requires  BodySite to be handled as a separate resource instead of an inline coded element (e.g. to identify and track separately)  then use the standard extension [bodySite](extension-bodysite.html).
   */
  bodySite?: CodeableConcept;
  /**
   * Time when specimen was collected from subject - the physiologically relevant time.
   */
  collectedDateTime?: string;
  _collectedDateTime?: Element;
  /**
   * Time when specimen was collected from subject - the physiologically relevant time.
   */
  collectedPeriod?: Period;
  /**
   * Person who collected the specimen.
   */
  collector?: Reference;
  /**
   * The span of time over which the collection of a specimen occurred.
   */
  duration?: Duration;
  /**
   * Representing fasting status using this element is preferred to representing it with an observation using a 'pre-coordinated code'  such as  LOINC 2005-7 (Calcium [Moles/​time] in 2 hour Urine --12 hours fasting), or  using  a component observation ` such as `Observation.component code`  = LOINC 49541-6 (Fasting status - Reported).
   */
  fastingStatusCodeableConcept?: CodeableConcept;
  /**
   * Representing fasting status using this element is preferred to representing it with an observation using a 'pre-coordinated code'  such as  LOINC 2005-7 (Calcium [Moles/​time] in 2 hour Urine --12 hours fasting), or  using  a component observation ` such as `Observation.component code`  = LOINC 49541-6 (Fasting status - Reported).
   */
  fastingStatusDuration?: Duration;
  /**
   * A coded value specifying the technique that is used to perform the procedure.
   */
  method?: CodeableConcept;
  /**
   * The quantity of specimen collected; for instance the volume of a blood sample, or the physical measurement of an anatomic pathology sample.
   */
  quantity?: Quantity;
}
/**
 * Details concerning processing and processing steps for the specimen.
 */
export interface SpecimenProcessing extends BackboneElement {
  /**
   * Material used in the processing step.
   */
  additive?: Reference[];
  /**
   * Textual description of procedure.
   */
  description?: string;
  _description?: Element;
  /**
   * A coded value specifying the procedure used to process the specimen.
   */
  procedure?: CodeableConcept;
  /**
   * A record of the time or period when the specimen processing occurred.  For example the time of sample fixation or the period of time the sample was in formalin.
   */
  timeDateTime?: string;
  _timeDateTime?: Element;
  /**
   * A record of the time or period when the specimen processing occurred.  For example the time of sample fixation or the period of time the sample was in formalin.
   */
  timePeriod?: Period;
}
/**
 * The container holding the specimen.  The recursive nature of containers; i.e. blood in tube in tray in rack is not addressed here.
 */
export interface SpecimenContainer extends BackboneElement {
  /**
   * Introduced substance to preserve, maintain or enhance the specimen. Examples: Formalin, Citrate, EDTA.
   */
  additiveCodeableConcept?: CodeableConcept;
  /**
   * Introduced substance to preserve, maintain or enhance the specimen. Examples: Formalin, Citrate, EDTA.
   */
  additiveReference?: Reference;
  /**
   * The capacity (volume or other measure) the container may contain.
   */
  capacity?: Quantity;
  /**
   * Textual description of the container.
   */
  description?: string;
  _description?: Element;
  /**
   * Id for container. There may be multiple; a manufacturer's bar code, lab assigned identifier, etc. The container ID may differ from the specimen id in some circumstances.
   */
  identifier?: Identifier[];
  /**
   * The quantity of specimen in the container; may be volume, dimensions, or other appropriate measurements, depending on the specimen type.
   */
  specimenQuantity?: Quantity;
  /**
   * The type of container associated with the specimen (e.g. slide, aliquot, etc.).
   */
  type?: CodeableConcept;
}
/**
 * A sample to be used for analysis.
 */
export interface Specimen extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Specimen';
  /**
   * The identifier assigned by the lab when accessioning specimen(s). This is not necessarily the same as the specimen identifier, depending on local lab procedures.
   */
  accessionIdentifier?: Identifier;
  /**
   * Details concerning the specimen collection.
   */
  collection?: SpecimenCollection;
  /**
   * Specimen condition is an observation made about the specimen.  It's a point-in-time assessment.  It can be used to assess its quality or appropriateness for a specific test.
   */
  condition?: CodeableConcept[];
  /**
   * The container holding the specimen.  The recursive nature of containers; i.e. blood in tube in tray in rack is not addressed here.
   */
  container?: SpecimenContainer[];
  /**
   * Id for specimen.
   */
  identifier?: Identifier[];
  /**
   * To communicate any details or issues about the specimen or during the specimen collection. (for example: broken vial, sent with patient, frozen).
   */
  note?: Annotation[];
  /**
   * The parent specimen could be the source from which the current specimen is derived by some processing step (e.g. an aliquot or isolate or extracted nucleic acids from clinical samples) or one of many specimens that were combined to create a pooled sample.
   */
  parent?: Reference[];
  /**
   * Details concerning processing and processing steps for the specimen.
   */
  processing?: SpecimenProcessing[];
  /**
   * Time when specimen was received for processing or testing.
   */
  receivedTime?: string;
  _receivedTime?: Element;
  /**
   * The request may be explicit or implied such with a ServiceRequest that requires a blood draw.
   */
  request?: Reference[];
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status?: string;
  _status?: Element;
  /**
   * Where the specimen came from. This may be from patient(s), from a location (e.g., the source of an environmental sample), or a sampling of a substance or a device.
   */
  subject?: Reference;
  /**
   * The type can change the way that a specimen is handled and drives what kind of analyses can properly be performed on the specimen. It is frequently used in diagnostic work flow decision making systems.
   */
  type?: CodeableConcept;
}
/**
 * Code Values for the Specimen.status field
 */
export enum SpecimenStatusCodes {
  AVAILABLE = 'available',
  UNAVAILABLE = 'unavailable',
  UNSATISFACTORY = 'unsatisfactory',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Substance introduced in the kind of container to preserve, maintain or enhance the specimen. Examples: Formalin, Citrate, EDTA.
 */
export interface SpecimenDefinitionTypeTestedContainerAdditive extends BackboneElement {
  /**
   * Substance introduced in the kind of container to preserve, maintain or enhance the specimen. Examples: Formalin, Citrate, EDTA.
   */
  additiveCodeableConcept: CodeableConcept;
  /**
   * Substance introduced in the kind of container to preserve, maintain or enhance the specimen. Examples: Formalin, Citrate, EDTA.
   */
  additiveReference: Reference;
}
/**
 * The specimen's container.
 */
export interface SpecimenDefinitionTypeTestedContainer extends BackboneElement {
  /**
   * Substance introduced in the kind of container to preserve, maintain or enhance the specimen. Examples: Formalin, Citrate, EDTA.
   */
  additive?: SpecimenDefinitionTypeTestedContainerAdditive[];
  /**
   * Color of container cap.
   */
  cap?: CodeableConcept;
  /**
   * The capacity (volume or other measure) of this kind of container.
   */
  capacity?: Quantity;
  /**
   * The textual description of the kind of container.
   */
  description?: string;
  _description?: Element;
  /**
   * The type of material of the container.
   */
  material?: CodeableConcept;
  /**
   * The minimum volume to be conditioned in the container.
   */
  minimumVolumeQuantity?: Quantity;
  /**
   * The minimum volume to be conditioned in the container.
   */
  minimumVolumeString?: string;
  _minimumVolumeString?: Element;
  /**
   * Special processing that should be applied to the container for this kind of specimen.
   */
  preparation?: string;
  _preparation?: Element;
  /**
   * The type of container used to contain this kind of specimen.
   */
  type?: CodeableConcept;
}
/**
 * Set of instructions for preservation/transport of the specimen at a defined temperature interval, prior the testing process.
 */
export interface SpecimenDefinitionTypeTestedHandling extends BackboneElement {
  /**
   * Additional textual instructions for the preservation or transport of the specimen. For instance, 'Protect from light exposure'.
   */
  instruction?: string;
  _instruction?: Element;
  /**
   * The maximum time interval of preservation of the specimen with these conditions.
   */
  maxDuration?: Duration;
  /**
   * It qualifies the interval of temperature, which characterizes an occurrence of handling. Conditions that are not related to temperature may be handled in the instruction element.
   */
  temperatureQualifier?: CodeableConcept;
  /**
   * The temperature interval for this set of handling instructions.
   */
  temperatureRange?: Range;
}
/**
 * Specimen conditioned in a container as expected by the testing laboratory.
 */
export interface SpecimenDefinitionTypeTested extends BackboneElement {
  /**
   * The specimen's container.
   */
  container?: SpecimenDefinitionTypeTestedContainer;
  /**
   * Set of instructions for preservation/transport of the specimen at a defined temperature interval, prior the testing process.
   */
  handling?: SpecimenDefinitionTypeTestedHandling[];
  /**
   * Primary of secondary specimen.
   */
  isDerived?: boolean;
  _isDerived?: Element;
  /**
   * The preference for this type of conditioned specimen.
   */
  preference: string;
  _preference?: Element;
  /**
   * Criterion for rejection of the specimen in its container by the laboratory.
   */
  rejectionCriterion?: CodeableConcept[];
  /**
   * Requirements for delivery and special handling of this kind of conditioned specimen.
   */
  requirement?: string;
  _requirement?: Element;
  /**
   * The usual time that a specimen of this kind is retained after the ordered tests are completed, for the purpose of additional testing.
   */
  retentionTime?: Duration;
  /**
   * The kind of specimen conditioned for testing expected by lab.
   */
  type?: CodeableConcept;
}
/**
 * Code Values for the SpecimenDefinition.typeTested.preference field
 */
export enum SpecimenDefinitionTypeTestedPreferenceCodes {
  PREFERRED = 'preferred',
  ALTERNATE = 'alternate',
}
/**
 * A kind of specimen with associated set of requirements.
 */
export interface SpecimenDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'SpecimenDefinition';
  /**
   * The action to be performed for collecting the specimen.
   */
  collection?: CodeableConcept[];
  /**
   * A business identifier associated with the kind of specimen.
   */
  identifier?: Identifier;
  /**
   * Preparation of the patient for specimen collection.
   */
  patientPreparation?: CodeableConcept[];
  /**
   * Time aspect of specimen collection (duration or offset).
   */
  timeAspect?: string;
  _timeAspect?: Element;
  /**
   * The kind of material to be collected.
   */
  typeCollected?: CodeableConcept;
  /**
   * Specimen conditioned in a container as expected by the testing laboratory.
   */
  typeTested?: SpecimenDefinitionTypeTested[];
}
/**
 * An external specification that the content is mapped to.
 */
export interface StructureDefinitionMapping extends BackboneElement {
  /**
   * Comments about this mapping, including version notes, issues, scope limitations, and other important notes for usage.
   */
  comment?: string;
  _comment?: Element;
  /**
   * The specification is described once, with general comments, and then specific mappings are made that reference this declaration.
   */
  identity: string;
  _identity?: Element;
  /**
   * A name for the specification that is being mapped to.
   */
  name?: string;
  _name?: Element;
  /**
   * A formal identity for the specification being mapped to helps with identifying maps consistently.
   */
  uri?: string;
  _uri?: Element;
}
/**
 * Identifies the types of resource or data type elements to which the extension can be applied.
 */
export interface StructureDefinitionContext extends BackboneElement {
  /**
   * An expression that defines where an extension can be used in resources.
   */
  expression: string;
  _expression?: Element;
  /**
   * Defines how to interpret the expression that defines what the context of the extension is.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the StructureDefinition.context.type field
 */
export enum StructureDefinitionContextTypeCodes {
  FHIRPATH = 'fhirpath',
  ELEMENT = 'element',
  EXTENSION = 'extension',
}
/**
 * A snapshot view is expressed in a standalone form that can be used and interpreted without considering the base StructureDefinition.
 */
export interface StructureDefinitionSnapshot extends BackboneElement {
  /**
   * Captures constraints on each element within the resource.
   */
  element: ElementDefinition[];
}
/**
 * A differential view is expressed relative to the base StructureDefinition - a statement of differences that it applies.
 */
export interface StructureDefinitionDifferential extends BackboneElement {
  /**
   * Captures constraints on each element within the resource.
   */
  element: ElementDefinition[];
}
/**
 * A definition of a FHIR structure. This resource is used to describe the underlying resources, data types defined in FHIR, and also for describing extensions and constraints on resources and data types.
 */
export interface StructureDefinition extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'StructureDefinition';
  /**
   * Abstract Resources cannot be instantiated - a concrete sub-type must be used. Abstract datatypes and extensions cannot be used in an instance. For logical models, the exact implication of "abstract" will rest with the author, depending how the logical model is used. Flagging a constraint structure as abstract conveys design intent but makes no difference to how the structure definition is handled. Note that inline declared elements that are given the type "Element" in the StructureDefinition, but have children described, are anonymous concrete types that specialize Element.
   */
  abstract: boolean;
  _abstract?: Element;
  /**
   * If differential constraints are specified in this structure, they are applied to the base in a "differential" fashion. If there is no base, then the differential constraints cannot be provided (snapshot only). Differential structures are useful for the editing perspective, and snapshot structures are suitable for operational use. The FHIR Project provides a number of tools/services to populate snapshots from differential constraints. Logical Models have a base of "Element" or another logical model.
   */
  baseDefinition?: string;
  _baseDefinition?: Element;
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * Identifies the types of resource or data type elements to which the extension can be applied.
   */
  context?: StructureDefinitionContext[];
  /**
   * The rules are only evaluated when the extension is present. When evaluating the invariant, the FHIRPath focus is the element that holds the extension, and %extension refers to the extension itself.
   */
  contextInvariant?: string[];
  _contextInvariant?: Element;
  /**
   * A copyright statement relating to the structure definition and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the structure definition.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the structure definition. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * If the definition is a specialization, then it adds new elements in the differential, and the snapshot includes the inherited elements.  If the definition is a constraint, then it cannot define new elements, it can only make new rules about existing content (see [Profiling Resources](profiling.html#resources)).
   */
  derivation?: string;
  _derivation?: Element;
  /**
   * This description can be used to capture details such as why the structure definition was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the structure definition as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the structure definition is presumed to be the predominant language in the place the structure definition was created).
   */
  description?: string;
  _description?: Element;
  /**
   * A differential view is expressed relative to the base StructureDefinition - a statement of differences that it applies.
   */
  differential?: StructureDefinitionDifferential;
  /**
   * Allows filtering of structure definitions that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * A StructureDefinition does not need to specify the target it applies to as StructureDefinitions will often be valid across multiple versions of FHIR. FHIR tooling can determine whether a StructureDefinition is consistent with a particular StructureDefinition if desired.
   */
  fhirVersion?: string;
  _fhirVersion?: Element;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this structure definition outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * It may be possible for the structure definition to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * A set of key words or terms from external terminologies that may be used to assist with indexing and searching of templates nby describing the use of this structure definition, or the content it describes.
   */
  keyword?: Coding[];
  /**
   * Defines the kind of structure that this definition is describing.
   */
  kind: string;
  _kind?: Element;
  /**
   * An external specification that the content is mapped to.
   */
  mapping?: StructureDefinitionMapping[];
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.Some Examples:
   * * O2SatObservation
   * * PresentationReport
   * * Immunization2
   * * AcmeAdmissionRecordOld.
   */
  name: string;
  _name?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the structure definition is the organization or individual primarily responsible for the maintenance and upkeep of the structure definition. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the structure definition. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the structure definition. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this structure definition.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * A snapshot view is expressed in a standalone form that can be used and interpreted without considering the base StructureDefinition.
   */
  snapshot?: StructureDefinitionSnapshot;
  /**
   * Allows filtering of structure definitions that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.Applications don't have to use this name but can always fall back to it. The title also corresponds to the label for the root element.
   */
  title?: string;
  _title?: Element;
  /**
   * Note that in the case of constraints, the type could be determined by chasing through the baseDefinition references until a concrete structure (derivation = specialization) is reached, or by looking at the path of the first element in the snapshot - if present - but providing the type directly makes for simpler tooling and indexing.
   * The type must match the elements defined in the differential and the snapshot. For all FHIR defined types, the path name of the element will start with the type name. For logical models, where the type is a URL, the type name SHOULD start with the tail of the type URL where required.
   */
  type: string;
  _type?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different structure definition instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the structure definition with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the StructureDefinition.derivation field
 */
export enum StructureDefinitionDerivationCodes {
  SPECIALIZATION = 'specialization',
  CONSTRAINT = 'constraint',
}
/**
 * Code Values for the StructureDefinition.kind field
 */
export enum StructureDefinitionKindCodes {
  PRIMITIVE_TYPE = 'primitive-type',
  COMPLEX_TYPE = 'complex-type',
  RESOURCE = 'resource',
  LOGICAL = 'logical',
}
/**
 * Code Values for the StructureDefinition.status field
 */
export enum StructureDefinitionStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * It is not necessary for a structure map to identify any dependent structures, though not listing them may restrict its usefulness.
 */
export interface StructureMapStructure extends BackboneElement {
  /**
   * This is needed if both types have the same name (e.g. version conversion).
   */
  alias?: string;
  _alias?: Element;
  /**
   * Documentation that describes how the structure is used in the mapping.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * How the referenced structure is used in this mapping.
   */
  mode: string;
  _mode?: Element;
  /**
   * The canonical reference to the structure.
   */
  url: string;
  _url?: Element;
}
/**
 * Code Values for the StructureMap.structure.mode field
 */
export enum StructureMapStructureModeCodes {
  SOURCE = 'source',
  QUERIED = 'queried',
  TARGET = 'target',
  PRODUCED = 'produced',
}
/**
 * If no inputs are named, then the entry mappings are type based.
 */
export interface StructureMapGroupInput extends BackboneElement {
  /**
   * Documentation for this instance of data.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * Mode for this instance of data.
   */
  mode: string;
  _mode?: Element;
  /**
   * Name for this instance of data.
   */
  name: string;
  _name?: Element;
  /**
   * Type for this instance of data.
   */
  type?: string;
  _type?: Element;
}
/**
 * Code Values for the StructureMap.group.input.mode field
 */
export enum StructureMapGroupInputModeCodes {
  SOURCE = 'source',
  TARGET = 'target',
}
/**
 * Source inputs to the mapping.
 */
export interface StructureMapGroupRuleSource extends BackboneElement {
  /**
   * FHIRPath expression  - must be true or the mapping engine throws an error instead of completing.
   */
  check?: string;
  _check?: Element;
  /**
   * FHIRPath expression  - must be true or the rule does not apply.
   */
  condition?: string;
  _condition?: Element;
  /**
   * Type or variable this rule applies to.
   */
  context: string;
  _context?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueBase64Binary?: string;
  _defaultValueBase64Binary?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueBoolean?: boolean;
  _defaultValueBoolean?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueCanonical?: string;
  _defaultValueCanonical?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueCode?: string;
  _defaultValueCode?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueDate?: string;
  _defaultValueDate?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueDateTime?: string;
  _defaultValueDateTime?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueDecimal?: number;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueId?: string;
  _defaultValueId?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueInstant?: string;
  _defaultValueInstant?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueInteger?: number;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueMarkdown?: string;
  _defaultValueMarkdown?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueOid?: string;
  _defaultValueOid?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValuePositiveInt?: number;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueString?: string;
  _defaultValueString?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueTime?: string;
  _defaultValueTime?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueUnsignedInt?: number;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueUri?: string;
  _defaultValueUri?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueUrl?: string;
  _defaultValueUrl?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueUuid?: string;
  _defaultValueUuid?: Element;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueAddress?: Address;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueAge?: Age;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueAnnotation?: Annotation;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueAttachment?: Attachment;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueCodeableConcept?: CodeableConcept;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueCoding?: Coding;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueContactPoint?: ContactPoint;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueCount?: Count;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueDistance?: Distance;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueDuration?: Duration;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueHumanName?: HumanName;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueIdentifier?: Identifier;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueMoney?: Money;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValuePeriod?: Period;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueQuantity?: Quantity;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueRange?: Range;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueRatio?: Ratio;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueReference?: Reference;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueSampledData?: SampledData;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueSignature?: Signature;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueTiming?: Timing;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueContactDetail?: ContactDetail;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueContributor?: Contributor;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueDataRequirement?: DataRequirement;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueExpression?: Expression;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueParameterDefinition?: ParameterDefinition;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueRelatedArtifact?: RelatedArtifact;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueTriggerDefinition?: TriggerDefinition;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueUsageContext?: UsageContext;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueDosage?: Dosage;
  /**
   * If there's a default value on an item that can repeat, it will only be used once.
   */
  defaultValueMeta?: Meta;
  /**
   * Optional field for this source.
   */
  element?: string;
  _element?: Element;
  /**
   * How to handle the list mode for this element.
   */
  listMode?: string;
  _listMode?: Element;
  /**
   * This is typically used for recording that something Is not transformed to the target for some reason.
   */
  logMessage?: string;
  _logMessage?: Element;
  /**
   * Specified maximum cardinality for the element - a number or a "*". This is optional; if present, it acts an implicit check on the input content (* just serves as documentation; it's the default value).
   */
  max?: string;
  _max?: Element;
  /**
   * Specified minimum cardinality for the element. This is optional; if present, it acts an implicit check on the input content.
   */
  min?: number;
  /**
   * Specified type for the element. This works as a condition on the mapping - use for polymorphic elements.
   */
  type?: string;
  _type?: Element;
  /**
   * Named context for field, if a field is specified.
   */
  variable?: string;
  _variable?: Element;
}
/**
 * Code Values for the StructureMap.group.rule.source.listMode field
 */
export enum StructureMapGroupRuleSourceListModeCodes {
  FIRST = 'first',
  NOT_FIRST = 'not_first',
  LAST = 'last',
  NOT_LAST = 'not_last',
  ONLY_ONE = 'only_one',
}
/**
 * Parameters to the transform.
 */
export interface StructureMapGroupRuleTargetParameter extends BackboneElement {
  /**
   * Parameter value - variable or literal.
   */
  valueId: string;
  _valueId?: Element;
  /**
   * Parameter value - variable or literal.
   */
  valueString: string;
  _valueString?: Element;
  /**
   * Parameter value - variable or literal.
   */
  valueBoolean: boolean;
  _valueBoolean?: Element;
  /**
   * Parameter value - variable or literal.
   */
  valueInteger: number;
  /**
   * Parameter value - variable or literal.
   */
  valueDecimal: number;
}
/**
 * Content to create because of this mapping rule.
 */
export interface StructureMapGroupRuleTarget extends BackboneElement {
  /**
   * Type or variable this rule applies to.
   */
  context?: string;
  _context?: Element;
  /**
   * How to interpret the context.
   */
  contextType?: string;
  _contextType?: Element;
  /**
   * Field to create in the context.
   */
  element?: string;
  _element?: Element;
  /**
   * If field is a list, how to manage the list.
   */
  listMode?: string[];
  _listMode?: Element;
  /**
   * Internal rule reference for shared list items.
   */
  listRuleId?: string;
  _listRuleId?: Element;
  /**
   * Parameters to the transform.
   */
  parameter?: StructureMapGroupRuleTargetParameter[];
  /**
   * How the data is copied / created.
   */
  transform?: string;
  _transform?: Element;
  /**
   * Named context for field, if desired, and a field is specified.
   */
  variable?: string;
  _variable?: Element;
}
/**
 * Code Values for the StructureMap.group.rule.target.contextType field
 */
export enum StructureMapGroupRuleTargetContextTypeCodes {
  TYPE = 'type',
  VARIABLE = 'variable',
}
/**
 * Code Values for the StructureMap.group.rule.target.listMode field
 */
export enum StructureMapGroupRuleTargetListModeCodes {
  FIRST = 'first',
  SHARE = 'share',
  LAST = 'last',
  COLLATE = 'collate',
}
/**
 * Code Values for the StructureMap.group.rule.target.transform field
 */
export enum StructureMapGroupRuleTargetTransformCodes {
  CREATE = 'create',
  COPY = 'copy',
}
/**
 * Which other rules to apply in the context of this rule.
 */
export interface StructureMapGroupRuleDependent extends BackboneElement {
  /**
   * Name of a rule or group to apply.
   */
  name: string;
  _name?: Element;
  /**
   * Variable to pass to the rule or group.
   */
  variable: string[];
  _variable?: Element;
}
/**
 * Transform Rule from source to target.
 */
export interface StructureMapGroupRule extends BackboneElement {
  /**
   * Which other rules to apply in the context of this rule.
   */
  dependent?: StructureMapGroupRuleDependent[];
  /**
   * Documentation for this instance of data.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * Name of the rule for internal references.
   */
  name: string;
  _name?: Element;
  /**
   * Rules contained in this rule.
   */
  rule?: StructureMapGroupRule[];
  /**
   * Source inputs to the mapping.
   */
  source: StructureMapGroupRuleSource[];
  /**
   * Content to create because of this mapping rule.
   */
  target?: StructureMapGroupRuleTarget[];
}
/**
 * Organizes the mapping into manageable chunks for human review/ease of maintenance.
 */
export interface StructureMapGroup extends BackboneElement {
  /**
   * Additional supporting documentation that explains the purpose of the group and the types of mappings within it.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * Another group that this group adds rules to.
   */
  extends?: string;
  _extends?: Element;
  /**
   * If no inputs are named, then the entry mappings are type based.
   */
  input: StructureMapGroupInput[];
  /**
   * A unique name for the group for the convenience of human readers.
   */
  name: string;
  _name?: Element;
  /**
   * Transform Rule from source to target.
   */
  rule: StructureMapGroupRule[];
  /**
   * Not applicable if the underlying model is untyped. There can only be one default mapping for any particular type combination.
   */
  typeMode: string;
  _typeMode?: Element;
}
/**
 * Code Values for the StructureMap.group.typeMode field
 */
export enum StructureMapGroupTypeModeCodes {
  NONE = 'none',
  TYPES = 'types',
  TYPE_AND_TYPES = 'type-and-types',
}
/**
 * A Map of relationships between 2 structures that can be used to transform data.
 */
export interface StructureMap extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'StructureMap';
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the structure map and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the structure map.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the structure map. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the structure map was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the structure map as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the structure map is presumed to be the predominant language in the place the structure map was created).
   */
  description?: string;
  _description?: Element;
  /**
   * Allows filtering of structure maps that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Organizes the mapping into manageable chunks for human review/ease of maintenance.
   */
  group: StructureMapGroup[];
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this structure map outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * Other maps used by this map (canonical URLs).
   */
  import?: string[];
  _import?: Element;
  /**
   * It may be possible for the structure map to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name: string;
  _name?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the structure map is the organization or individual primarily responsible for the maintenance and upkeep of the structure map. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the structure map. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the structure map. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this structure map.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Allows filtering of structure maps that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * It is not necessary for a structure map to identify any dependent structures, though not listing them may restrict its usefulness.
   */
  structure?: StructureMapStructure[];
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different structure map instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the structure map with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the StructureMap.status field
 */
export enum StructureMapStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Details where to send notifications when resources are received that meet the criteria.
 */
export interface SubscriptionChannel extends BackboneElement {
  /**
   * For rest-hook, and websocket, the end-point must be an http: or https: URL; for email, a mailto: url, for sms, a tel: url, and for message the endpoint can be in any form of url the server understands (usually, http: or mllp:). The URI is allowed to be relative; in which case, it is relative to the server end-point (since there may be more than one, clients should avoid using relative URIs).
   */
  endpoint?: string;
  _endpoint?: Element;
  /**
   * Exactly what these mean depend on the channel type. They can convey additional information to the recipient and/or meet security requirements; for example, support of multiple headers in the outgoing notifications for rest-hook type subscriptions.
   */
  header?: string[];
  _header?: Element;
  /**
   * Sending the payload has obvious security implications. The server is responsible for ensuring that the content is appropriately secured.
   */
  payload?: string;
  _payload?: Element;
  /**
   * The type of channel to send notifications on.
   */
  type: string;
  _type?: Element;
}
/**
 * Code Values for the Subscription.channel.type field
 */
export enum SubscriptionChannelTypeCodes {
  REST_HOOK = 'rest-hook',
  WEBSOCKET = 'websocket',
  EMAIL = 'email',
  SMS = 'sms',
  MESSAGE = 'message',
}
/**
 * The subscription resource is used to define a push-based subscription from a server to another system. Once a subscription is registered with the server, the server checks every resource that is created or updated, and if the resource matches the given criteria, it sends a message on the defined "channel" so that another system can take an appropriate action.
 */
export interface Subscription extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Subscription';
  /**
   * Details where to send notifications when resources are received that meet the criteria.
   */
  channel: SubscriptionChannel;
  /**
   * Contact details for a human to contact about the subscription. The primary use of this for system administrator troubleshooting.
   */
  contact?: ContactPoint[];
  /**
   * The rules are search criteria (without the [base] part). Like Bundle.entry.request.url, it has no leading "/".
   */
  criteria: string;
  _criteria?: Element;
  /**
   * The server is permitted to deviate from this time but should observe it.
   */
  end?: string;
  _end?: Element;
  /**
   * A record of the last error that occurred when the server processed a notification.
   */
  error?: string;
  _error?: Element;
  /**
   * A description of why this subscription is defined.
   */
  reason: string;
  _reason?: Element;
  /**
   * A client can only submit subscription resources in the requested or off state. Only the server can  move a subscription from requested to active, and then to error. Either the server or the client can turn a subscription off.
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
}
/**
 * Code Values for the Subscription.status field
 */
export enum SubscriptionStatusCodes {
  REQUESTED = 'requested',
  ACTIVE = 'active',
  ERROR = 'error',
  OFF = 'off',
}
/**
 * Substance may be used to describe a kind of substance, or a specific package/container of the substance: an instance.
 */
export interface SubstanceInstance extends BackboneElement {
  /**
   * When the substance is no longer valid to use. For some substances, a single arbitrary date is used for expiry.
   */
  expiry?: string;
  _expiry?: Element;
  /**
   * Identifier associated with the package/container (usually a label affixed directly).
   */
  identifier?: Identifier;
  /**
   * The amount of the substance.
   */
  quantity?: Quantity;
}
/**
 * A substance can be composed of other substances.
 */
export interface SubstanceIngredient extends BackboneElement {
  /**
   * The amount of the ingredient in the substance - a concentration ratio.
   */
  quantity?: Ratio;
  /**
   * Another substance that is a component of this substance.
   */
  substanceCodeableConcept: CodeableConcept;
  /**
   * Another substance that is a component of this substance.
   */
  substanceReference: Reference;
}
/**
 * A homogeneous material with a definite composition.
 */
export interface Substance extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Substance';
  /**
   * The level of granularity is defined by the category concepts in the value set.   More fine-grained filtering can be performed using the metadata and/or terminology hierarchy in Substance.code.
   */
  category?: CodeableConcept[];
  /**
   * This could be a reference to an externally defined code.  It could also be a locally assigned code (e.g. a formulary),  optionally with translations to the standard drug codes.
   */
  code: CodeableConcept;
  /**
   * A description of the substance - its appearance, handling requirements, and other usage notes.
   */
  description?: string;
  _description?: Element;
  /**
   * This identifier is associated with the kind of substance in contrast to the  Substance.instance.identifier which is associated with the package/container.
   */
  identifier?: Identifier[];
  /**
   * A substance can be composed of other substances.
   */
  ingredient?: SubstanceIngredient[];
  /**
   * Substance may be used to describe a kind of substance, or a specific package/container of the substance: an instance.
   */
  instance?: SubstanceInstance[];
  /**
   * A code to indicate if the substance is actively used.
   */
  status?: string;
  _status?: Element;
}
/**
 * Code Values for the Substance.status field
 */
export enum SubstanceStatusCodes {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * The linkages between sugar residues will also be captured.
 */
export interface SubstanceNucleicAcidSubunitLinkage extends BackboneElement {
  /**
   * The entity that links the sugar residues together should also be captured for nearly all naturally occurring nucleic acid the linkage is a phosphate group. For many synthetic oligonucleotides phosphorothioate linkages are often seen. Linkage connectivity is assumed to be 3’-5’. If the linkage is either 3’-3’ or 5’-5’ this should be specified.
   */
  connectivity?: string;
  _connectivity?: Element;
  /**
   * Each linkage will be registered as a fragment and have an ID.
   */
  identifier?: Identifier;
  /**
   * Each linkage will be registered as a fragment and have at least one name. A single name shall be assigned to each linkage.
   */
  name?: string;
  _name?: Element;
  /**
   * Residues shall be captured as described in 5.3.6.8.3.
   */
  residueSite?: string;
  _residueSite?: Element;
}
/**
 * 5.3.6.8.1 Sugar ID (Mandatory).
 */
export interface SubstanceNucleicAcidSubunitSugar extends BackboneElement {
  /**
   * The Substance ID of the sugar or sugar-like component that make up the nucleotide.
   */
  identifier?: Identifier;
  /**
   * The name of the sugar or sugar-like component that make up the nucleotide.
   */
  name?: string;
  _name?: Element;
  /**
   * The residues that contain a given sugar will be captured. The order of given residues will be captured in the 5‘-3‘direction consistent with the base sequences listed above.
   */
  residueSite?: string;
  _residueSite?: Element;
}
/**
 * Subunits are listed in order of decreasing length; sequences of the same length will be ordered by molecular weight; subunits that have identical sequences will be repeated multiple times.
 */
export interface SubstanceNucleicAcidSubunit extends BackboneElement {
  /**
   * The nucleotide present at the 5’ terminal shall be specified based on a controlled vocabulary. Since the sequence is represented from the 5' to the 3' end, the 5’ prime nucleotide is the letter at the first position in the sequence. A separate representation would be redundant.
   */
  fivePrime?: CodeableConcept;
  /**
   * The length of the sequence shall be captured.
   */
  length?: number;
  /**
   * The linkages between sugar residues will also be captured.
   */
  linkage?: SubstanceNucleicAcidSubunitLinkage[];
  /**
   * Actual nucleotide sequence notation from 5' to 3' end using standard single letter codes. In addition to the base sequence, sugar and type of phosphate or non-phosphate linkage should also be captured.
   */
  sequence?: string;
  _sequence?: Element;
  /**
   * (TBC).
   */
  sequenceAttachment?: Attachment;
  /**
   * Index of linear sequences of nucleic acids in order of decreasing length. Sequences of the same length will be ordered by molecular weight. Subunits that have identical sequences will be repeated and have sequential subscripts.
   */
  subunit?: number;
  /**
   * 5.3.6.8.1 Sugar ID (Mandatory).
   */
  sugar?: SubstanceNucleicAcidSubunitSugar[];
  /**
   * The nucleotide present at the 3’ terminal shall be specified based on a controlled vocabulary. Since the sequence is represented from the 5' to the 3' end, the 5’ prime nucleotide is the letter at the last position in the sequence. A separate representation would be redundant.
   */
  threePrime?: CodeableConcept;
}
/**
 * Nucleic acids are defined by three distinct elements: the base, sugar and linkage. Individual substance/moiety IDs will be created for each of these elements. The nucleotide sequence will be always entered in the 5’-3’ direction.
 */
export interface SubstanceNucleicAcid extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'SubstanceNucleicAcid';
  /**
   * The area of hybridisation shall be described if applicable for double stranded RNA or DNA. The number associated with the subunit followed by the number associated to the residue shall be specified in increasing order. The underscore “” shall be used as separator as follows: “Subunitnumber Residue”.
   */
  areaOfHybridisation?: string;
  _areaOfHybridisation?: Element;
  /**
   * The number of linear sequences of nucleotides linked through phosphodiester bonds shall be described. Subunits would be strands of nucleic acids that are tightly associated typically through Watson-Crick base pairing. NOTE: If not specified in the reference source, the assumption is that there is 1 subunit.
   */
  numberOfSubunits?: number;
  /**
   * (TBC).
   */
  oligoNucleotideType?: CodeableConcept;
  /**
   * The type of the sequence shall be specified based on a controlled vocabulary.
   */
  sequenceType?: CodeableConcept;
  /**
   * Subunits are listed in order of decreasing length; sequences of the same length will be ordered by molecular weight; subunits that have identical sequences will be repeated multiple times.
   */
  subunit?: SubstanceNucleicAcidSubunit[];
}
/**
 * Todo.
 */
export interface SubstancePolymerMonomerSetStartingMaterial extends BackboneElement {
  /**
   * Todo.
   */
  amount?: SubstanceAmount;
  /**
   * Todo.
   */
  isDefining?: boolean;
  _isDefining?: Element;
  /**
   * Todo.
   */
  material?: CodeableConcept;
  /**
   * Todo.
   */
  type?: CodeableConcept;
}
/**
 * Todo.
 */
export interface SubstancePolymerMonomerSet extends BackboneElement {
  /**
   * Todo.
   */
  ratioType?: CodeableConcept;
  /**
   * Todo.
   */
  startingMaterial?: SubstancePolymerMonomerSetStartingMaterial[];
}
/**
 * Todo.
 */
export interface SubstancePolymerRepeatRepeatUnitDegreeOfPolymerisation extends BackboneElement {
  /**
   * Todo.
   */
  amount?: SubstanceAmount;
  /**
   * Todo.
   */
  degree?: CodeableConcept;
}
/**
 * Todo.
 */
export interface SubstancePolymerRepeatRepeatUnitStructuralRepresentation extends BackboneElement {
  /**
   * Todo.
   */
  attachment?: Attachment;
  /**
   * Todo.
   */
  representation?: string;
  _representation?: Element;
  /**
   * Todo.
   */
  type?: CodeableConcept;
}
/**
 * Todo.
 */
export interface SubstancePolymerRepeatRepeatUnit extends BackboneElement {
  /**
   * Todo.
   */
  amount?: SubstanceAmount;
  /**
   * Todo.
   */
  degreeOfPolymerisation?: SubstancePolymerRepeatRepeatUnitDegreeOfPolymerisation[];
  /**
   * Todo.
   */
  orientationOfPolymerisation?: CodeableConcept;
  /**
   * Todo.
   */
  repeatUnit?: string;
  _repeatUnit?: Element;
  /**
   * Todo.
   */
  structuralRepresentation?: SubstancePolymerRepeatRepeatUnitStructuralRepresentation[];
}
/**
 * Todo.
 */
export interface SubstancePolymerRepeat extends BackboneElement {
  /**
   * Todo.
   */
  averageMolecularFormula?: string;
  _averageMolecularFormula?: Element;
  /**
   * Todo.
   */
  numberOfUnits?: number;
  /**
   * Todo.
   */
  repeatUnit?: SubstancePolymerRepeatRepeatUnit[];
  /**
   * Todo.
   */
  repeatUnitAmountType?: CodeableConcept;
}
/**
 * Todo.
 */
export interface SubstancePolymer extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'SubstancePolymer';
  /**
   * Todo.
   */
  class?: CodeableConcept;
  /**
   * Todo.
   */
  copolymerConnectivity?: CodeableConcept[];
  /**
   * Todo.
   */
  geometry?: CodeableConcept;
  /**
   * Todo.
   */
  modification?: string[];
  _modification?: Element;
  /**
   * Todo.
   */
  monomerSet?: SubstancePolymerMonomerSet[];
  /**
   * Todo.
   */
  repeat?: SubstancePolymerRepeat[];
}
/**
 * This subclause refers to the description of each subunit constituting the SubstanceProtein. A subunit is a linear sequence of amino acids linked through peptide bonds. The Subunit information shall be provided when the finished SubstanceProtein is a complex of multiple sequences; subunits are not used to delineate domains within a single sequence. Subunits are listed in order of decreasing length; sequences of the same length will be ordered by decreasing molecular weight; subunits that have identical sequences will be repeated multiple times.
 */
export interface SubstanceProteinSubunit extends BackboneElement {
  /**
   * The modification at the C-terminal shall be specified.
   */
  cTerminalModification?: string;
  _cTerminalModification?: Element;
  /**
   * Unique identifier for molecular fragment modification based on the ISO 11238 Substance ID.
   */
  cTerminalModificationId?: Identifier;
  /**
   * Length of linear sequences of amino acids contained in the subunit.
   */
  length?: number;
  /**
   * The name of the fragment modified at the N-terminal of the SubstanceProtein shall be specified.
   */
  nTerminalModification?: string;
  _nTerminalModification?: Element;
  /**
   * Unique identifier for molecular fragment modification based on the ISO 11238 Substance ID.
   */
  nTerminalModificationId?: Identifier;
  /**
   * The sequence information shall be provided enumerating the amino acids from N- to C-terminal end using standard single-letter amino acid codes. Uppercase shall be used for L-amino acids and lowercase for D-amino acids. Transcribed SubstanceProteins will always be described using the translated sequence; for synthetic peptide containing amino acids that are not represented with a single letter code an X should be used within the sequence. The modified amino acids will be distinguished by their position in the sequence.
   */
  sequence?: string;
  _sequence?: Element;
  /**
   * The sequence information shall be provided enumerating the amino acids from N- to C-terminal end using standard single-letter amino acid codes. Uppercase shall be used for L-amino acids and lowercase for D-amino acids. Transcribed SubstanceProteins will always be described using the translated sequence; for synthetic peptide containing amino acids that are not represented with a single letter code an X should be used within the sequence. The modified amino acids will be distinguished by their position in the sequence.
   */
  sequenceAttachment?: Attachment;
  /**
   * Index of primary sequences of amino acids linked through peptide bonds in order of decreasing length. Sequences of the same length will be ordered by molecular weight. Subunits that have identical sequences will be repeated and have sequential subscripts.
   */
  subunit?: number;
}
/**
 * A SubstanceProtein is defined as a single unit of a linear amino acid sequence, or a combination of subunits that are either covalently linked or have a defined invariant stoichiometric relationship. This includes all synthetic, recombinant and purified SubstanceProteins of defined sequence, whether the use is therapeutic or prophylactic. This set of elements will be used to describe albumins, coagulation factors, cytokines, growth factors, peptide/SubstanceProtein hormones, enzymes, toxins, toxoids, recombinant vaccines, and immunomodulators.
 */
export interface SubstanceProtein extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'SubstanceProtein';
  /**
   * The disulphide bond between two cysteine residues either on the same subunit or on two different subunits shall be described. The position of the disulfide bonds in the SubstanceProtein shall be listed in increasing order of subunit number and position within subunit followed by the abbreviation of the amino acids involved. The disulfide linkage positions shall actually contain the amino acid Cysteine at the respective positions.
   */
  disulfideLinkage?: string[];
  _disulfideLinkage?: Element;
  /**
   * Number of linear sequences of amino acids linked through peptide bonds. The number of subunits constituting the SubstanceProtein shall be described. It is possible that the number of subunits can be variable.
   */
  numberOfSubunits?: number;
  /**
   * The SubstanceProtein descriptive elements will only be used when a complete or partial amino acid sequence is available or derivable from a nucleic acid sequence.
   */
  sequenceType?: CodeableConcept;
  /**
   * This subclause refers to the description of each subunit constituting the SubstanceProtein. A subunit is a linear sequence of amino acids linked through peptide bonds. The Subunit information shall be provided when the finished SubstanceProtein is a complex of multiple sequences; subunits are not used to delineate domains within a single sequence. Subunits are listed in order of decreasing length; sequences of the same length will be ordered by decreasing molecular weight; subunits that have identical sequences will be repeated multiple times.
   */
  subunit?: SubstanceProteinSubunit[];
}
/**
 * Todo.
 */
export interface SubstanceReferenceInformationGene extends BackboneElement {
  /**
   * Todo.
   */
  gene?: CodeableConcept;
  /**
   * Todo.
   */
  geneSequenceOrigin?: CodeableConcept;
  /**
   * Todo.
   */
  source?: Reference[];
}
/**
 * Todo.
 */
export interface SubstanceReferenceInformationGeneElement extends BackboneElement {
  /**
   * Todo.
   */
  element?: Identifier;
  /**
   * Todo.
   */
  source?: Reference[];
  /**
   * Todo.
   */
  type?: CodeableConcept;
}
/**
 * Todo.
 */
export interface SubstanceReferenceInformationClassification extends BackboneElement {
  /**
   * Todo.
   */
  classification?: CodeableConcept;
  /**
   * Todo.
   */
  domain?: CodeableConcept;
  /**
   * Todo.
   */
  source?: Reference[];
  /**
   * Todo.
   */
  subtype?: CodeableConcept[];
}
/**
 * Todo.
 */
export interface SubstanceReferenceInformationTarget extends BackboneElement {
  /**
   * Todo.
   */
  amountQuantity?: Quantity;
  /**
   * Todo.
   */
  amountRange?: Range;
  /**
   * Todo.
   */
  amountString?: string;
  _amountString?: Element;
  /**
   * Todo.
   */
  amountType?: CodeableConcept;
  /**
   * Todo.
   */
  interaction?: CodeableConcept;
  /**
   * Todo.
   */
  organism?: CodeableConcept;
  /**
   * Todo.
   */
  organismType?: CodeableConcept;
  /**
   * Todo.
   */
  source?: Reference[];
  /**
   * Todo.
   */
  target?: Identifier;
  /**
   * Todo.
   */
  type?: CodeableConcept;
}
/**
 * Todo.
 */
export interface SubstanceReferenceInformation extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'SubstanceReferenceInformation';
  /**
   * Todo.
   */
  classification?: SubstanceReferenceInformationClassification[];
  /**
   * Todo.
   */
  comment?: string;
  _comment?: Element;
  /**
   * Todo.
   */
  gene?: SubstanceReferenceInformationGene[];
  /**
   * Todo.
   */
  geneElement?: SubstanceReferenceInformationGeneElement[];
  /**
   * Todo.
   */
  target?: SubstanceReferenceInformationTarget[];
}
/**
 * Many complex materials are fractions of parts of plants, animals, or minerals. Fraction elements are often necessary to define both Substances and Specified Group 1 Substances. For substances derived from Plants, fraction information will be captured at the Substance information level ( . Oils, Juices and Exudates). Additional information for Extracts, such as extraction solvent composition, will be captured at the Specified Substance Group 1 information level. For plasma-derived products fraction information will be captured at the Substance and the Specified Substance Group 1 levels.
 */
export interface SubstanceSourceMaterialFractionDescription extends BackboneElement {
  /**
   * This element is capturing information about the fraction of a plant part, or human plasma for fractionation.
   */
  fraction?: string;
  _fraction?: Element;
  /**
   * The specific type of the material constituting the component. For Herbal preparations the particulars of the extracts (liquid/dry) is described in Specified Substance Group 1.
   */
  materialType?: CodeableConcept;
}
/**
 * 4.9.13.6.1 Author type (Conditional).
 */
export interface SubstanceSourceMaterialOrganismAuthor extends BackboneElement {
  /**
   * The author of an organism species shall be specified. The author year of an organism shall also be specified when applicable; refers to the year in which the first author(s) published the infraspecific plant/animal name (of any rank).
   */
  authorDescription?: string;
  _authorDescription?: Element;
  /**
   * The type of author of an organism species shall be specified. The parenthetical author of an organism species refers to the first author who published the plant/animal name (of any rank). The primary author of an organism species refers to the first author(s), who validly published the plant/animal name.
   */
  authorType?: CodeableConcept;
}
/**
 * 4.9.13.8.1 Hybrid species maternal organism ID (Optional).
 */
export interface SubstanceSourceMaterialOrganismHybrid extends BackboneElement {
  /**
   * The hybrid type of an organism shall be specified.
   */
  hybridType?: CodeableConcept;
  /**
   * The identifier of the maternal species constituting the hybrid organism shall be specified based on a controlled vocabulary. For plants, the parents aren’t always known, and it is unlikely that it will be known which is maternal and which is paternal.
   */
  maternalOrganismId?: string;
  _maternalOrganismId?: Element;
  /**
   * The name of the maternal species constituting the hybrid organism shall be specified. For plants, the parents aren’t always known, and it is unlikely that it will be known which is maternal and which is paternal.
   */
  maternalOrganismName?: string;
  _maternalOrganismName?: Element;
  /**
   * The identifier of the paternal species constituting the hybrid organism shall be specified based on a controlled vocabulary.
   */
  paternalOrganismId?: string;
  _paternalOrganismId?: Element;
  /**
   * The name of the paternal species constituting the hybrid organism shall be specified.
   */
  paternalOrganismName?: string;
  _paternalOrganismName?: Element;
}
/**
 * 4.9.13.7.1 Kingdom (Conditional).
 */
export interface SubstanceSourceMaterialOrganismOrganismGeneral extends BackboneElement {
  /**
   * The class of an organism shall be specified.
   */
  class?: CodeableConcept;
  /**
   * The kingdom of an organism shall be specified.
   */
  kingdom?: CodeableConcept;
  /**
   * The order of an organism shall be specified,.
   */
  order?: CodeableConcept;
  /**
   * The phylum of an organism shall be specified.
   */
  phylum?: CodeableConcept;
}
/**
 * This subclause describes the organism which the substance is derived from. For vaccines, the parent organism shall be specified based on these subclause elements. As an example, full taxonomy will be described for the Substance Name: ., Leaf.
 */
export interface SubstanceSourceMaterialOrganism extends BackboneElement {
  /**
   * 4.9.13.6.1 Author type (Conditional).
   */
  author?: SubstanceSourceMaterialOrganismAuthor[];
  /**
   * The family of an organism shall be specified.
   */
  family?: CodeableConcept;
  /**
   * The genus of an organism shall be specified; refers to the Latin epithet of the genus element of the plant/animal scientific name; it is present in names for genera, species and infraspecies.
   */
  genus?: CodeableConcept;
  /**
   * 4.9.13.8.1 Hybrid species maternal organism ID (Optional).
   */
  hybrid?: SubstanceSourceMaterialOrganismHybrid;
  /**
   * The intraspecific description of an organism shall be specified based on a controlled vocabulary. For Influenza Vaccine, the intraspecific description shall contain the syntax of the antigen in line with the WHO convention.
   */
  intraspecificDescription?: string;
  _intraspecificDescription?: Element;
  /**
   * The Intraspecific type of an organism shall be specified.
   */
  intraspecificType?: CodeableConcept;
  /**
   * 4.9.13.7.1 Kingdom (Conditional).
   */
  organismGeneral?: SubstanceSourceMaterialOrganismOrganismGeneral;
  /**
   * The species of an organism shall be specified; refers to the Latin epithet of the species of the plant/animal; it is present in names for species and infraspecies.
   */
  species?: CodeableConcept;
}
/**
 * To do.
 */
export interface SubstanceSourceMaterialPartDescription extends BackboneElement {
  /**
   * Entity of anatomical origin of source material within an organism.
   */
  part?: CodeableConcept;
  /**
   * The detailed anatomic location when the part can be extracted from different anatomical locations of the organism. Multiple alternative locations may apply.
   */
  partLocation?: CodeableConcept;
}
/**
 * Source material shall capture information on the taxonomic and anatomical origins as well as the fraction of a material that can result in or can be modified to form a substance. This set of data elements shall be used to define polymer substances isolated from biological matrices. Taxonomic and anatomical origins shall be described using a controlled vocabulary as required. This information is captured for naturally derived polymers ( . starch) and structurally diverse substances. For Organisms belonging to the Kingdom Plantae the Substance level defines the fresh material of a single species or infraspecies, the Herbal Drug and the Herbal preparation. For Herbal preparations, the fraction information will be captured at the Substance information level and additional information for herbal extracts will be captured at the Specified Substance Group 1 information level. See for further explanation the Substance Class: Structurally Diverse and the herbal annex.
 */
export interface SubstanceSourceMaterial extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'SubstanceSourceMaterial';
  /**
   * The country where the plant material is harvested or the countries where the plasma is sourced from as laid down in accordance with the Plasma Master File. For “Plasma-derived substances” the attribute country of origin provides information about the countries used for the manufacturing of the Cryopoor plama or Crioprecipitate.
   */
  countryOfOrigin?: CodeableConcept[];
  /**
   * Stage of life for animals, plants, insects and microorganisms. This information shall be provided only when the substance is significantly different in these stages (e.g. foetal bovine serum).
   */
  developmentStage?: CodeableConcept;
  /**
   * Many complex materials are fractions of parts of plants, animals, or minerals. Fraction elements are often necessary to define both Substances and Specified Group 1 Substances. For substances derived from Plants, fraction information will be captured at the Substance information level ( . Oils, Juices and Exudates). Additional information for Extracts, such as extraction solvent composition, will be captured at the Specified Substance Group 1 information level. For plasma-derived products fraction information will be captured at the Substance and the Specified Substance Group 1 levels.
   */
  fractionDescription?: SubstanceSourceMaterialFractionDescription[];
  /**
   * The place/region where the plant is harvested or the places/regions where the animal source material has its habitat.
   */
  geographicalLocation?: string[];
  _geographicalLocation?: Element;
  /**
   * This subclause describes the organism which the substance is derived from. For vaccines, the parent organism shall be specified based on these subclause elements. As an example, full taxonomy will be described for the Substance Name: ., Leaf.
   */
  organism?: SubstanceSourceMaterialOrganism;
  /**
   * The unique identifier associated with the source material parent organism shall be specified.
   */
  organismId?: Identifier;
  /**
   * The organism accepted Scientific name shall be provided based on the organism taxonomy.
   */
  organismName?: string;
  _organismName?: Element;
  /**
   * The parent of the herbal drug Ginkgo biloba, Leaf is the substance ID of the substance (fresh) of Ginkgo biloba L. or Ginkgo biloba L. (Whole plant).
   */
  parentSubstanceId?: Identifier[];
  /**
   * The parent substance of the Herbal Drug, or Herbal preparation.
   */
  parentSubstanceName?: string[];
  _parentSubstanceName?: Element;
  /**
   * To do.
   */
  partDescription?: SubstanceSourceMaterialPartDescription[];
  /**
   * General high level classification of the source material specific to the origin of the material.
   */
  sourceMaterialClass?: CodeableConcept;
  /**
   * The state of the source material when extracted.
   */
  sourceMaterialState?: CodeableConcept;
  /**
   * The type of the source material shall be specified based on a controlled vocabulary. For vaccines, this subclause refers to the class of infectious agent.
   */
  sourceMaterialType?: CodeableConcept;
}
/**
 * Moiety, for structural modifications.
 */
export interface SubstanceSpecificationMoiety extends BackboneElement {
  /**
   * Quantitative value for this moiety.
   */
  amountQuantity?: Quantity;
  /**
   * Quantitative value for this moiety.
   */
  amountString?: string;
  _amountString?: Element;
  /**
   * Identifier by which this moiety substance is known.
   */
  identifier?: Identifier;
  /**
   * Molecular formula.
   */
  molecularFormula?: string;
  _molecularFormula?: Element;
  /**
   * Textual name for this moiety substance.
   */
  name?: string;
  _name?: Element;
  /**
   * Optical activity type.
   */
  opticalActivity?: CodeableConcept;
  /**
   * Role that the moiety is playing.
   */
  role?: CodeableConcept;
  /**
   * Stereochemistry type.
   */
  stereochemistry?: CodeableConcept;
}
/**
 * General specifications for this substance, including how it is related to other substances.
 */
export interface SubstanceSpecificationProperty extends BackboneElement {
  /**
   * Quantitative value for this property.
   */
  amountQuantity?: Quantity;
  /**
   * Quantitative value for this property.
   */
  amountString?: string;
  _amountString?: Element;
  /**
   * A category for this property, e.g. Physical, Chemical, Enzymatic.
   */
  category?: CodeableConcept;
  /**
   * Property type e.g. viscosity, pH, isoelectric point.
   */
  code?: CodeableConcept;
  /**
   * A substance upon which a defining property depends (e.g. for solubility: in water, in alcohol).
   */
  definingSubstanceReference?: Reference;
  /**
   * A substance upon which a defining property depends (e.g. for solubility: in water, in alcohol).
   */
  definingSubstanceCodeableConcept?: CodeableConcept;
  /**
   * Parameters that were used in the measurement of a property (e.g. for viscosity: measured at 20C with a pH of 7.1).
   */
  parameters?: string;
  _parameters?: Element;
}
/**
 * The molecular weight or weight range (for proteins, polymers or nucleic acids).
 */
export interface SubstanceSpecificationStructureIsotopeMolecularWeight extends BackboneElement {
  /**
   * Used to capture quantitative values for a variety of elements. If only limits are given, the arithmetic mean would be the average. If only a single definite value for a given element is given, it would be captured in this field.
   */
  amount?: Quantity;
  /**
   * The method by which the molecular weight was determined.
   */
  method?: CodeableConcept;
  /**
   * Type of molecular weight such as exact, average (also known as. number average), weight average.
   */
  type?: CodeableConcept;
}
/**
 * Applicable for single substances that contain a radionuclide or a non-natural isotopic ratio.
 */
export interface SubstanceSpecificationStructureIsotope extends BackboneElement {
  /**
   * Half life - for a non-natural nuclide.
   */
  halfLife?: Quantity;
  /**
   * Substance identifier for each non-natural or radioisotope.
   */
  identifier?: Identifier;
  /**
   * The molecular weight or weight range (for proteins, polymers or nucleic acids).
   */
  molecularWeight?: SubstanceSpecificationStructureIsotopeMolecularWeight;
  /**
   * Substance name for each non-natural or radioisotope.
   */
  name?: CodeableConcept;
  /**
   * The type of isotopic substitution present in a single substance.
   */
  substitution?: CodeableConcept;
}
/**
 * Molecular structural representation.
 */
export interface SubstanceSpecificationStructureRepresentation extends BackboneElement {
  /**
   * An attached file with the structural representation.
   */
  attachment?: Attachment;
  /**
   * The structural representation as text string in a format e.g. InChI, SMILES, MOLFILE, CDX.
   */
  representation?: string;
  _representation?: Element;
  /**
   * The type of structure (e.g. Full, Partial, Representative).
   */
  type?: CodeableConcept;
}
/**
 * Structural information.
 */
export interface SubstanceSpecificationStructure extends BackboneElement {
  /**
   * Applicable for single substances that contain a radionuclide or a non-natural isotopic ratio.
   */
  isotope?: SubstanceSpecificationStructureIsotope[];
  /**
   * Molecular formula.
   */
  molecularFormula?: string;
  _molecularFormula?: Element;
  /**
   * Specified per moiety according to the Hill system, i.e. first C, then H, then alphabetical, each moiety separated by a dot.
   */
  molecularFormulaByMoiety?: string;
  _molecularFormulaByMoiety?: Element;
  /**
   * The molecular weight or weight range (for proteins, polymers or nucleic acids).
   */
  molecularWeight?: SubstanceSpecificationStructureIsotopeMolecularWeight;
  /**
   * Optical activity type.
   */
  opticalActivity?: CodeableConcept;
  /**
   * Molecular structural representation.
   */
  representation?: SubstanceSpecificationStructureRepresentation[];
  /**
   * Supporting literature.
   */
  source?: Reference[];
  /**
   * Stereochemistry type.
   */
  stereochemistry?: CodeableConcept;
}
/**
 * Codes associated with the substance.
 */
export interface SubstanceSpecificationCode extends BackboneElement {
  /**
   * The specific code.
   */
  code?: CodeableConcept;
  /**
   * Any comment can be provided in this field, if necessary.
   */
  comment?: string;
  _comment?: Element;
  /**
   * Supporting literature.
   */
  source?: Reference[];
  /**
   * Status of the code assignment.
   */
  status?: CodeableConcept;
  /**
   * The date at which the code status is changed as part of the terminology maintenance.
   */
  statusDate?: string;
  _statusDate?: Element;
}
/**
 * Details of the official nature of this name.
 */
export interface SubstanceSpecificationNameOfficial extends BackboneElement {
  /**
   * Which authority uses this official name.
   */
  authority?: CodeableConcept;
  /**
   * Date of official name change.
   */
  date?: string;
  _date?: Element;
  /**
   * The status of the official name.
   */
  status?: CodeableConcept;
}
/**
 * Names applicable to this substance.
 */
export interface SubstanceSpecificationName extends BackboneElement {
  /**
   * The use context of this name for example if there is a different name a drug active ingredient as opposed to a food colour additive.
   */
  domain?: CodeableConcept[];
  /**
   * The jurisdiction where this name applies.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * Language of the name.
   */
  language?: CodeableConcept[];
  /**
   * The actual name.
   */
  name: string;
  _name?: Element;
  /**
   * Details of the official nature of this name.
   */
  official?: SubstanceSpecificationNameOfficial[];
  /**
   * If this is the preferred name for this substance.
   */
  preferred?: boolean;
  _preferred?: Element;
  /**
   * Supporting literature.
   */
  source?: Reference[];
  /**
   * The status of the name.
   */
  status?: CodeableConcept;
  /**
   * A synonym of this name.
   */
  synonym?: SubstanceSpecificationName[];
  /**
   * A translation for this name.
   */
  translation?: SubstanceSpecificationName[];
  /**
   * Name type.
   */
  type?: CodeableConcept;
}
/**
 * A link between this substance and another, with details of the relationship.
 */
export interface SubstanceSpecificationRelationship extends BackboneElement {
  /**
   * A numeric factor for the relationship, for instance to express that the salt of a substance has some percentage of the active substance in relation to some other.
   */
  amountQuantity?: Quantity;
  /**
   * A numeric factor for the relationship, for instance to express that the salt of a substance has some percentage of the active substance in relation to some other.
   */
  amountRange?: Range;
  /**
   * A numeric factor for the relationship, for instance to express that the salt of a substance has some percentage of the active substance in relation to some other.
   */
  amountRatio?: Ratio;
  /**
   * A numeric factor for the relationship, for instance to express that the salt of a substance has some percentage of the active substance in relation to some other.
   */
  amountString?: string;
  _amountString?: Element;
  /**
   * For use when the numeric.
   */
  amountRatioLowLimit?: Ratio;
  /**
   * An operator for the amount, for example "average", "approximately", "less than".
   */
  amountType?: CodeableConcept;
  /**
   * For example where an enzyme strongly bonds with a particular substance, this is a defining relationship for that enzyme, out of several possible substance relationships.
   */
  isDefining?: boolean;
  _isDefining?: Element;
  /**
   * For example "salt to parent", "active moiety", "starting material".
   */
  relationship?: CodeableConcept;
  /**
   * Supporting literature.
   */
  source?: Reference[];
  /**
   * A pointer to another substance, as a resource or just a representational code.
   */
  substanceReference?: Reference;
  /**
   * A pointer to another substance, as a resource or just a representational code.
   */
  substanceCodeableConcept?: CodeableConcept;
}
/**
 * The detailed description of a substance, typically at a level beyond what is used for prescribing.
 */
export interface SubstanceSpecification extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'SubstanceSpecification';
  /**
   * Codes associated with the substance.
   */
  code?: SubstanceSpecificationCode[];
  /**
   * Textual comment about this record of a substance.
   */
  comment?: string;
  _comment?: Element;
  /**
   * Textual description of the substance.
   */
  description?: string;
  _description?: Element;
  /**
   * If the substance applies to only human or veterinary use.
   */
  domain?: CodeableConcept;
  /**
   * Identifier by which this substance is known.
   */
  identifier?: Identifier;
  /**
   * Moiety, for structural modifications.
   */
  moiety?: SubstanceSpecificationMoiety[];
  /**
   * The molecular weight or weight range (for proteins, polymers or nucleic acids).
   */
  molecularWeight?: SubstanceSpecificationStructureIsotopeMolecularWeight[];
  /**
   * Names applicable to this substance.
   */
  name?: SubstanceSpecificationName[];
  /**
   * Data items specific to nucleic acids.
   */
  nucleicAcid?: Reference;
  /**
   * Data items specific to polymers.
   */
  polymer?: Reference;
  /**
   * General specifications for this substance, including how it is related to other substances.
   */
  property?: SubstanceSpecificationProperty[];
  /**
   * Data items specific to proteins.
   */
  protein?: Reference;
  /**
   * General information detailing this substance.
   */
  referenceInformation?: Reference;
  /**
   * A link between this substance and another, with details of the relationship.
   */
  relationship?: SubstanceSpecificationRelationship[];
  /**
   * Supporting literature.
   */
  source?: Reference[];
  /**
   * Material or taxonomic/anatomical source for the substance.
   */
  sourceMaterial?: Reference;
  /**
   * Status of substance within the catalogue e.g. approved.
   */
  status?: CodeableConcept;
  /**
   * Structural information.
   */
  structure?: SubstanceSpecificationStructure;
  /**
   * High level categorization, e.g. polymer or nucleic acid.
   */
  type?: CodeableConcept;
}
/**
 * The item that is being delivered or has been supplied.
 */
export interface SupplyDeliverySuppliedItem extends BackboneElement {
  /**
   * Identifies the medication, substance or device being dispensed. This is either a link to a resource representing the details of the item or a code that identifies the item from a known list.
   */
  itemCodeableConcept?: CodeableConcept;
  /**
   * Identifies the medication, substance or device being dispensed. This is either a link to a resource representing the details of the item or a code that identifies the item from a known list.
   */
  itemReference?: Reference;
  /**
   * The amount of supply that has been dispensed. Includes unit of measure.
   */
  quantity?: Quantity;
}
/**
 * Record of delivery of what is supplied.
 */
export interface SupplyDelivery extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'SupplyDelivery';
  /**
   * A plan, proposal or order that is fulfilled in whole or in part by this event.
   */
  basedOn?: Reference[];
  /**
   * Identification of the facility/location where the Supply was shipped to, as part of the dispense event.
   */
  destination?: Reference;
  /**
   * This identifier is typically assigned by the dispenser, and may be used to reference the delivery when exchanging information about it with other systems.
   */
  identifier?: Identifier[];
  /**
   * [The list of types may be constrained as appropriate for the type of event].
   */
  occurrenceDateTime?: string;
  _occurrenceDateTime?: Element;
  /**
   * [The list of types may be constrained as appropriate for the type of event].
   */
  occurrencePeriod?: Period;
  /**
   * [The list of types may be constrained as appropriate for the type of event].
   */
  occurrenceTiming?: Timing;
  /**
   * Not to be used to link an event to an Encounter - use Event.context for that.
   * [The allowed reference resources may be adjusted as appropriate for the event resource].
   */
  partOf?: Reference[];
  /**
   * A link to a resource representing the person whom the delivered item is for.
   */
  patient?: Reference;
  /**
   * Identifies the person who picked up the Supply.
   */
  receiver?: Reference[];
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status?: string;
  _status?: Element;
  /**
   * The item that is being delivered or has been supplied.
   */
  suppliedItem?: SupplyDeliverySuppliedItem;
  /**
   * The individual responsible for dispensing the medication, supplier or device.
   */
  supplier?: Reference;
  /**
   * Indicates the type of dispensing event that is performed. Examples include: Trial Fill, Completion of Trial, Partial Fill, Emergency Fill, Samples, etc.
   */
  type?: CodeableConcept;
}
/**
 * Code Values for the SupplyDelivery.status field
 */
export enum SupplyDeliveryStatusCodes {
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  ABANDONED = 'abandoned',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * Specific parameters for the ordered item.  For example, the size of the indicated item.
 */
export interface SupplyRequestParameter extends BackboneElement {
  /**
   * A code or string that identifies the device detail being asserted.
   */
  code?: CodeableConcept;
  /**
   * Range means device should have a value that falls somewhere within the specified range.
   */
  valueCodeableConcept?: CodeableConcept;
  /**
   * Range means device should have a value that falls somewhere within the specified range.
   */
  valueQuantity?: Quantity;
  /**
   * Range means device should have a value that falls somewhere within the specified range.
   */
  valueRange?: Range;
  /**
   * Range means device should have a value that falls somewhere within the specified range.
   */
  valueBoolean?: boolean;
  _valueBoolean?: Element;
}
/**
 * A record of a request for a medication, substance or device used in the healthcare setting.
 */
export interface SupplyRequest extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'SupplyRequest';
  /**
   * When the request was made.
   */
  authoredOn?: string;
  _authoredOn?: Element;
  /**
   * Category of supply, e.g.  central, non-stock, etc. This is used to support work flows associated with the supply process.
   */
  category?: CodeableConcept;
  /**
   * Where the supply is expected to come from.
   */
  deliverFrom?: Reference;
  /**
   * Where the supply is destined to go.
   */
  deliverTo?: Reference;
  /**
   * The identifier.type element is used to distinguish between the identifiers assigned by the requester/placer and the performer/filler.
   */
  identifier?: Identifier[];
  /**
   * Note that there's a difference between a prescription - an instruction to take a medication, along with a (sometimes) implicit supply, and an explicit request to supply, with no explicit instructions.
   */
  itemCodeableConcept: CodeableConcept;
  /**
   * Note that there's a difference between a prescription - an instruction to take a medication, along with a (sometimes) implicit supply, and an explicit request to supply, with no explicit instructions.
   */
  itemReference: Reference;
  /**
   * When the request should be fulfilled.
   */
  occurrenceDateTime?: string;
  _occurrenceDateTime?: Element;
  /**
   * When the request should be fulfilled.
   */
  occurrencePeriod?: Period;
  /**
   * When the request should be fulfilled.
   */
  occurrenceTiming?: Timing;
  /**
   * Specific parameters for the ordered item.  For example, the size of the indicated item.
   */
  parameter?: SupplyRequestParameter[];
  /**
   * Indicates how quickly this SupplyRequest should be addressed with respect to other requests.
   */
  priority?: string;
  _priority?: Element;
  /**
   * The amount that is being ordered of the indicated item.
   */
  quantity: Quantity;
  /**
   * The reason why the supply item was requested.
   */
  reasonCode?: CodeableConcept[];
  /**
   * The reason why the supply item was requested.
   */
  reasonReference?: Reference[];
  /**
   * The device, practitioner, etc. who initiated the request.
   */
  requester?: Reference;
  /**
   * Status of the supply request.
   */
  status?: string;
  _status?: Element;
  /**
   * Who is intended to fulfill the request.
   */
  supplier?: Reference[];
}
/**
 * Code Values for the SupplyRequest.priority field
 */
export enum SupplyRequestPriorityCodes {
  ROUTINE = 'routine',
  URGENT = 'urgent',
  ASAP = 'asap',
  STAT = 'stat',
}
/**
 * Code Values for the SupplyRequest.status field
 */
export enum SupplyRequestStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
}
/**
 * If the Task.focus is a request resource and the task is seeking fulfillment (i.e. is asking for the request to be actioned), this element identifies any limitations on what parts of the referenced request should be actioned.
 */
export interface TaskRestriction extends BackboneElement {
  /**
   * Note that period.high is the due date representing the time by which the task should be completed.
   */
  period?: Period;
  /**
   * For requests that are targeted to more than on potential recipient/target, for whom is fulfillment sought?
   */
  recipient?: Reference[];
  /**
   * Indicates the number of times the requested action should occur.
   */
  repetitions?: number;
}
/**
 * Additional information that may be needed in the execution of the task.
 */
export interface TaskInput extends BackboneElement {
  /**
   * If referencing a BPMN workflow or Protocol, the "system" is the URL for the workflow definition and the code is the "name" of the required input.
   */
  type: CodeableConcept;
  /**
   * The value of the input parameter as a basic type.
   */
  valueBase64Binary: string;
  _valueBase64Binary?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueBoolean: boolean;
  _valueBoolean?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueCanonical: string;
  _valueCanonical?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueCode: string;
  _valueCode?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueDate: string;
  _valueDate?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueDateTime: string;
  _valueDateTime?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueDecimal: number;
  /**
   * The value of the input parameter as a basic type.
   */
  valueId: string;
  _valueId?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueInstant: string;
  _valueInstant?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueInteger: number;
  /**
   * The value of the input parameter as a basic type.
   */
  valueMarkdown: string;
  _valueMarkdown?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueOid: string;
  _valueOid?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valuePositiveInt: number;
  /**
   * The value of the input parameter as a basic type.
   */
  valueString: string;
  _valueString?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueTime: string;
  _valueTime?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueUnsignedInt: number;
  /**
   * The value of the input parameter as a basic type.
   */
  valueUri: string;
  _valueUri?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueUrl: string;
  _valueUrl?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueUuid: string;
  _valueUuid?: Element;
  /**
   * The value of the input parameter as a basic type.
   */
  valueAddress: Address;
  /**
   * The value of the input parameter as a basic type.
   */
  valueAge: Age;
  /**
   * The value of the input parameter as a basic type.
   */
  valueAnnotation: Annotation;
  /**
   * The value of the input parameter as a basic type.
   */
  valueAttachment: Attachment;
  /**
   * The value of the input parameter as a basic type.
   */
  valueCodeableConcept: CodeableConcept;
  /**
   * The value of the input parameter as a basic type.
   */
  valueCoding: Coding;
  /**
   * The value of the input parameter as a basic type.
   */
  valueContactPoint: ContactPoint;
  /**
   * The value of the input parameter as a basic type.
   */
  valueCount: Count;
  /**
   * The value of the input parameter as a basic type.
   */
  valueDistance: Distance;
  /**
   * The value of the input parameter as a basic type.
   */
  valueDuration: Duration;
  /**
   * The value of the input parameter as a basic type.
   */
  valueHumanName: HumanName;
  /**
   * The value of the input parameter as a basic type.
   */
  valueIdentifier: Identifier;
  /**
   * The value of the input parameter as a basic type.
   */
  valueMoney: Money;
  /**
   * The value of the input parameter as a basic type.
   */
  valuePeriod: Period;
  /**
   * The value of the input parameter as a basic type.
   */
  valueQuantity: Quantity;
  /**
   * The value of the input parameter as a basic type.
   */
  valueRange: Range;
  /**
   * The value of the input parameter as a basic type.
   */
  valueRatio: Ratio;
  /**
   * The value of the input parameter as a basic type.
   */
  valueReference: Reference;
  /**
   * The value of the input parameter as a basic type.
   */
  valueSampledData: SampledData;
  /**
   * The value of the input parameter as a basic type.
   */
  valueSignature: Signature;
  /**
   * The value of the input parameter as a basic type.
   */
  valueTiming: Timing;
  /**
   * The value of the input parameter as a basic type.
   */
  valueContactDetail: ContactDetail;
  /**
   * The value of the input parameter as a basic type.
   */
  valueContributor: Contributor;
  /**
   * The value of the input parameter as a basic type.
   */
  valueDataRequirement: DataRequirement;
  /**
   * The value of the input parameter as a basic type.
   */
  valueExpression: Expression;
  /**
   * The value of the input parameter as a basic type.
   */
  valueParameterDefinition: ParameterDefinition;
  /**
   * The value of the input parameter as a basic type.
   */
  valueRelatedArtifact: RelatedArtifact;
  /**
   * The value of the input parameter as a basic type.
   */
  valueTriggerDefinition: TriggerDefinition;
  /**
   * The value of the input parameter as a basic type.
   */
  valueUsageContext: UsageContext;
  /**
   * The value of the input parameter as a basic type.
   */
  valueDosage: Dosage;
  /**
   * The value of the input parameter as a basic type.
   */
  valueMeta: Meta;
}
/**
 * Outputs produced by the Task.
 */
export interface TaskOutput extends BackboneElement {
  /**
   * The name of the Output parameter.
   */
  type: CodeableConcept;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueBase64Binary: string;
  _valueBase64Binary?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueBoolean: boolean;
  _valueBoolean?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueCanonical: string;
  _valueCanonical?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueCode: string;
  _valueCode?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueDate: string;
  _valueDate?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueDateTime: string;
  _valueDateTime?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueDecimal: number;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueId: string;
  _valueId?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueInstant: string;
  _valueInstant?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueInteger: number;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueMarkdown: string;
  _valueMarkdown?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueOid: string;
  _valueOid?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valuePositiveInt: number;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueString: string;
  _valueString?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueTime: string;
  _valueTime?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueUnsignedInt: number;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueUri: string;
  _valueUri?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueUrl: string;
  _valueUrl?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueUuid: string;
  _valueUuid?: Element;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueAddress: Address;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueAge: Age;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueAnnotation: Annotation;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueAttachment: Attachment;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueCodeableConcept: CodeableConcept;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueCoding: Coding;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueContactPoint: ContactPoint;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueCount: Count;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueDistance: Distance;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueDuration: Duration;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueHumanName: HumanName;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueIdentifier: Identifier;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueMoney: Money;
  /**
   * The value of the Output parameter as a basic type.
   */
  valuePeriod: Period;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueQuantity: Quantity;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueRange: Range;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueRatio: Ratio;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueReference: Reference;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueSampledData: SampledData;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueSignature: Signature;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueTiming: Timing;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueContactDetail: ContactDetail;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueContributor: Contributor;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueDataRequirement: DataRequirement;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueExpression: Expression;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueParameterDefinition: ParameterDefinition;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueRelatedArtifact: RelatedArtifact;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueTriggerDefinition: TriggerDefinition;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueUsageContext: UsageContext;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueDosage: Dosage;
  /**
   * The value of the Output parameter as a basic type.
   */
  valueMeta: Meta;
}
/**
 * A task to be performed.
 */
export interface Task extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'Task';
  /**
   * The date and time this task was created.
   */
  authoredOn?: string;
  _authoredOn?: Element;
  /**
   * BasedOn refers to a higher-level authorization that triggered the creation of the task.  It references a "request" resource such as a ServiceRequest, MedicationRequest, ServiceRequest, CarePlan, etc. which is distinct from the "request" resource the task is seeking to fulfill.  This latter resource is referenced by FocusOn.  For example, based on a ServiceRequest (= BasedOn), a task is created to fulfill a procedureRequest ( = FocusOn ) to collect a specimen from a patient.
   */
  basedOn?: Reference[];
  /**
   * Contains business-specific nuances of the business state.
   */
  businessStatus?: CodeableConcept;
  /**
   * The title (eg "My Tasks", "Outstanding Tasks for Patient X") should go into the code.
   */
  code?: CodeableConcept;
  /**
   * A free-text description of what is to be performed.
   */
  description?: string;
  _description?: Element;
  /**
   * The healthcare event  (e.g. a patient and healthcare provider interaction) during which this task was created.
   */
  encounter?: Reference;
  /**
   * Identifies the time action was first taken against the task (start) and/or the time final action was taken against the task prior to marking it as completed (end).
   */
  executionPeriod?: Period;
  /**
   * If multiple resources need to be manipulated, use sub-tasks.  (This ensures that status can be tracked independently for each referenced resource.).
   */
  focus?: Reference;
  /**
   * The entity who benefits from the performance of the service specified in the task (e.g., the patient).
   */
  for?: Reference;
  /**
   * An identifier that links together multiple tasks and other requests that were created in the same context.
   */
  groupIdentifier?: Identifier;
  /**
   * The business identifier for this task.
   */
  identifier?: Identifier[];
  /**
   * Additional information that may be needed in the execution of the task.
   */
  input?: TaskInput[];
  /**
   * The URL pointing to a *FHIR*-defined protocol, guideline, orderset or other definition that is adhered to in whole or in part by this Task.
   */
  instantiatesCanonical?: string;
  _instantiatesCanonical?: Element;
  /**
   * The URL pointing to an *externally* maintained  protocol, guideline, orderset or other definition that is adhered to in whole or in part by this Task.
   */
  instantiatesUri?: string;
  _instantiatesUri?: Element;
  /**
   * Insurance plans, coverage extensions, pre-authorizations and/or pre-determinations that may be relevant to the Task.
   */
  insurance?: Reference[];
  /**
   * This element is immutable.  Proposed tasks, planned tasks, etc. must be distinct instances.
   * In most cases, Tasks will have an intent of "order".
   */
  intent: string;
  _intent?: Element;
  /**
   * The date and time of last modification to this task.
   */
  lastModified?: string;
  _lastModified?: Element;
  /**
   * Principal physical location where the this task is performed.
   */
  location?: Reference;
  /**
   * Free-text information captured about the task as it progresses.
   */
  note?: Annotation[];
  /**
   * Outputs produced by the Task.
   */
  output?: TaskOutput[];
  /**
   * Tasks may be created with an owner not yet identified.
   */
  owner?: Reference;
  /**
   * This should usually be 0..1.
   */
  partOf?: Reference[];
  /**
   * The kind of participant that should perform the task.
   */
  performerType?: CodeableConcept[];
  /**
   * Indicates how quickly the Task should be addressed with respect to other requests.
   */
  priority?: string;
  _priority?: Element;
  /**
   * This should only be included if there is no focus or if it differs from the reason indicated on the focus.
   */
  reasonCode?: CodeableConcept;
  /**
   * Tasks might be justified based on an Observation, a Condition, a past or planned procedure, etc.   This should only be included if there is no focus or if it differs from the reason indicated on the focus.    Use the CodeableConcept text element in `Task.reasonCode` if the data is free (uncoded) text.
   */
  reasonReference?: Reference;
  /**
   * This element does not point to the Provenance associated with the *current* version of the resource - as it would be created after this version existed.  The Provenance for the current version can be retrieved with a _revinclude.
   */
  relevantHistory?: Reference[];
  /**
   * The creator of the task.
   */
  requester?: Reference;
  /**
   * If the Task.focus is a request resource and the task is seeking fulfillment (i.e. is asking for the request to be actioned), this element identifies any limitations on what parts of the referenced request should be actioned.
   */
  restriction?: TaskRestriction;
  /**
   * The current status of the task.
   */
  status: string;
  _status?: Element;
  /**
   * This applies to the current status.  Look at the history of the task to see reasons for past statuses.
   */
  statusReason?: CodeableConcept;
}
/**
 * Code Values for the Task.intent field
 */
export enum TaskIntentCodes {
  UNKNOWN = 'unknown',
  PROPOSAL = 'proposal',
  PLAN = 'plan',
  ORDER = 'order',
  ORIGINAL_ORDER = 'original-order',
  REFLEX_ORDER = 'reflex-order',
  FILLER_ORDER = 'filler-order',
  INSTANCE_ORDER = 'instance-order',
  OPTION = 'option',
}
/**
 * Code Values for the Task.priority field
 */
export enum TaskPriorityCodes {
  ROUTINE = 'routine',
  URGENT = 'urgent',
  ASAP = 'asap',
  STAT = 'stat',
}
/**
 * Code Values for the Task.status field
 */
export enum TaskStatusCodes {
  DRAFT = 'draft',
  REQUESTED = 'requested',
  RECEIVED = 'received',
  ACCEPTED = 'accepted',
  PLUS = '+',
}
/**
 * Software that is covered by this terminology capability statement.  It is used when the statement describes the capabilities of a particular software version, independent of an installation.
 */
export interface TerminologyCapabilitiesSoftware extends BackboneElement {
  /**
   * Name the software is known by.
   */
  name: string;
  _name?: Element;
  /**
   * If possible, a version should be specified, as statements are likely to be different for different versions of software.
   */
  version?: string;
  _version?: Element;
}
/**
 * Identifies a specific implementation instance that is described by the terminology capability statement - i.e. a particular installation, rather than the capabilities of a software program.
 */
export interface TerminologyCapabilitiesImplementation extends BackboneElement {
  /**
   * Information about the specific installation that this terminology capability statement relates to.
   */
  description: string;
  _description?: Element;
  /**
   * An absolute base URL for the implementation.
   */
  url?: string;
  _url?: Element;
}
/**
 * Filter Properties supported.
 */
export interface TerminologyCapabilitiesCodeSystemVersionFilter extends BackboneElement {
  /**
   * Code of the property supported.
   */
  code: string;
  _code?: Element;
  /**
   * Operations supported for the property.
   */
  op: string[];
  _op?: Element;
}
/**
 * Language translations might not be available for all codes.
 */
export interface TerminologyCapabilitiesCodeSystemVersion extends BackboneElement {
  /**
   * For version-less code systems, there should be a single version with no identifier.
   */
  code?: string;
  _code?: Element;
  /**
   * If the compositional grammar defined by the code system is supported.
   */
  compositional?: boolean;
  _compositional?: Element;
  /**
   * Filter Properties supported.
   */
  filter?: TerminologyCapabilitiesCodeSystemVersionFilter[];
  /**
   * If this is the default version for this code system.
   */
  isDefault?: boolean;
  _isDefault?: Element;
  /**
   * Language Displays supported.
   */
  language?: string[];
  _language?: Element;
  /**
   * Properties supported for $lookup.
   */
  property?: string[];
  _property?: Element;
}
/**
 * The code system - identified by its system URL - may also be declared explicitly as a Code System Resource at /CodeSystem, but it might not be.
 */
export interface TerminologyCapabilitiesCodeSystem extends BackboneElement {
  /**
   * True if subsumption is supported for this version of the code system.
   */
  subsumption?: boolean;
  _subsumption?: Element;
  /**
   * URI for the Code System.
   */
  uri?: string;
  _uri?: Element;
  /**
   * Language translations might not be available for all codes.
   */
  version?: TerminologyCapabilitiesCodeSystemVersion[];
}
/**
 * Supported expansion parameter.
 */
export interface TerminologyCapabilitiesExpansionParameter extends BackboneElement {
  /**
   * Description of support for parameter.
   */
  documentation?: string;
  _documentation?: Element;
  /**
   * Expansion Parameter name.
   */
  name: string;
  _name?: Element;
}
/**
 * Information about the [ValueSet/$expand](valueset-operation-expand.html) operation.
 */
export interface TerminologyCapabilitiesExpansion extends BackboneElement {
  /**
   * Whether the server can return nested value sets.
   */
  hierarchical?: boolean;
  _hierarchical?: Element;
  /**
   * Allow request for incomplete expansions?
   */
  incomplete?: boolean;
  _incomplete?: Element;
  /**
   * Whether the server supports paging on expansion.
   */
  paging?: boolean;
  _paging?: Element;
  /**
   * Supported expansion parameter.
   */
  parameter?: TerminologyCapabilitiesExpansionParameter[];
  /**
   * This documentation should cover things like case sensitivity,  use of punctuation if not ignored, what wild cards are supported (if any), whether text is starts with or contains, and whether word order matters.
   */
  textFilter?: string;
  _textFilter?: Element;
}
/**
 * Information about the [ValueSet/$validate-code](valueset-operation-validate-code.html) operation.
 */
export interface TerminologyCapabilitiesValidateCode extends BackboneElement {
  /**
   * Whether translations are validated.
   */
  translations: boolean;
  _translations?: Element;
}
/**
 * Information about the [ConceptMap/$translate](conceptmap-operation-translate.html) operation.
 */
export interface TerminologyCapabilitiesTranslation extends BackboneElement {
  /**
   * Whether the client must identify the map.
   */
  needsMap: boolean;
  _needsMap?: Element;
}
/**
 * Whether the $closure operation is supported.
 */
export interface TerminologyCapabilitiesClosure extends BackboneElement {
  /**
   * If cross-system closure is supported.
   */
  translation?: boolean;
  _translation?: Element;
}
/**
 * A TerminologyCapabilities resource documents a set of capabilities (behaviors) of a FHIR Terminology Server that may be used as a statement of actual server functionality or a statement of required or desired server implementation.
 */
export interface TerminologyCapabilities extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'TerminologyCapabilities';
  /**
   * Whether the $closure operation is supported.
   */
  closure?: TerminologyCapabilitiesClosure;
  /**
   * See notes on the [ValueSet](valueset.html#) resource.
   */
  codeSearch?: string;
  _codeSearch?: Element;
  /**
   * The code system - identified by its system URL - may also be declared explicitly as a Code System Resource at /CodeSystem, but it might not be.
   */
  codeSystem?: TerminologyCapabilitiesCodeSystem[];
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the terminology capabilities and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the terminology capabilities.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the terminology capabilities. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the terminology capabilities was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the terminology capabilities as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the terminology capabilities is presumed to be the predominant language in the place the terminology capabilities was created).This does not need to be populated if the description is adequately implied by the software or implementation details.
   */
  description?: string;
  _description?: Element;
  /**
   * Information about the [ValueSet/$expand](valueset-operation-expand.html) operation.
   */
  expansion?: TerminologyCapabilitiesExpansion;
  /**
   * Allows filtering of terminology capabilitiess that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Identifies a specific implementation instance that is described by the terminology capability statement - i.e. a particular installation, rather than the capabilities of a software program.
   */
  implementation?: TerminologyCapabilitiesImplementation;
  /**
   * It may be possible for the terminology capabilities to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * The way that this statement is intended to be used, to describe an actual running instance of software, a particular product (kind, not instance of software) or a class of implementation (e.g. a desired purchase).
   */
  kind: string;
  _kind?: Element;
  /**
   * Whether the server supports lockedDate.
   */
  lockedDate?: boolean;
  _lockedDate?: Element;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name?: string;
  _name?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the terminology capabilities is the organization or individual primarily responsible for the maintenance and upkeep of the terminology capabilities. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the terminology capabilities. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the terminology capabilities. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this terminology capabilities.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Software that is covered by this terminology capability statement.  It is used when the statement describes the capabilities of a particular software version, independent of an installation.
   */
  software?: TerminologyCapabilitiesSoftware;
  /**
   * Allows filtering of terminology capabilitiess that are appropriate for use versus not.This is not intended for use with actual capability statements, but where capability statements are used to describe possible or desired systems.
   */
  status: string;
  _status?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Information about the [ConceptMap/$translate](conceptmap-operation-translate.html) operation.
   */
  translation?: TerminologyCapabilitiesTranslation;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * Information about the [ValueSet/$validate-code](valueset-operation-validate-code.html) operation.
   */
  validateCode?: TerminologyCapabilitiesValidateCode;
  /**
   * There may be different terminology capabilities instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the terminology capabilities with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the TerminologyCapabilities.codeSearch field
 */
export enum TerminologyCapabilitiesCodeSearchCodes {
  EXPLICIT = 'explicit',
  ALL = 'all',
}
/**
 * Code Values for the TerminologyCapabilities.kind field
 */
export enum TerminologyCapabilitiesKindCodes {
  INSTANCE = 'instance',
  CAPABILITY = 'capability',
  REQUIREMENTS = 'requirements',
}
/**
 * Code Values for the TerminologyCapabilities.status field
 */
export enum TerminologyCapabilitiesStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * A participant in the test execution, either the execution engine, a client, or a server.
 */
export interface TestReportParticipant extends BackboneElement {
  /**
   * The display name of the participant.
   */
  display?: string;
  _display?: Element;
  /**
   * The type of participant.
   */
  type: string;
  _type?: Element;
  /**
   * The uri of the participant. An absolute URL is preferred.
   */
  uri: string;
  _uri?: Element;
}
/**
 * Code Values for the TestReport.participant.type field
 */
export enum TestReportParticipantTypeCodes {
  TEST_ENGINE = 'test-engine',
  CLIENT = 'client',
  SERVER = 'server',
}
/**
 * The operation performed.
 */
export interface TestReportSetupActionOperation extends BackboneElement {
  /**
   * A link to further details on the result.
   */
  detail?: string;
  _detail?: Element;
  /**
   * An explanatory message associated with the result.
   */
  message?: string;
  _message?: Element;
  /**
   * The result of this operation.
   */
  result: string;
  _result?: Element;
}
/**
 * Code Values for the TestReport.setup.action.operation.result field
 */
export enum TestReportSetupActionOperationResultCodes {
  PASS = 'pass',
  SKIP = 'skip',
  FAIL = 'fail',
  WARNING = 'warning',
  ERROR = 'error',
}
/**
 * The results of the assertion performed on the previous operations.
 */
export interface TestReportSetupActionAssert extends BackboneElement {
  /**
   * A link to further details on the result.
   */
  detail?: string;
  _detail?: Element;
  /**
   * An explanatory message associated with the result.
   */
  message?: string;
  _message?: Element;
  /**
   * The result of this assertion.
   */
  result: string;
  _result?: Element;
}
/**
 * Code Values for the TestReport.setup.action.assert.result field
 */
export enum TestReportSetupActionAssertResultCodes {
  PASS = 'pass',
  SKIP = 'skip',
  FAIL = 'fail',
  WARNING = 'warning',
  ERROR = 'error',
}
/**
 * An action should contain either an operation or an assertion but not both.  It can contain any number of variables.
 */
export interface TestReportSetupAction extends BackboneElement {
  /**
   * The results of the assertion performed on the previous operations.
   */
  assert?: TestReportSetupActionAssert;
  /**
   * The operation performed.
   */
  operation?: TestReportSetupActionOperation;
}
/**
 * The results of the series of required setup operations before the tests were executed.
 */
export interface TestReportSetup extends BackboneElement {
  /**
   * An action should contain either an operation or an assertion but not both.  It can contain any number of variables.
   */
  action: TestReportSetupAction[];
}
/**
 * An action should contain either an operation or an assertion but not both.  It can contain any number of variables.
 */
export interface TestReportTestAction extends BackboneElement {
  /**
   * The results of the assertion performed on the previous operations.
   */
  assert?: TestReportSetupActionAssert;
  /**
   * An operation would involve a REST request to a server.
   */
  operation?: TestReportSetupActionOperation;
}
/**
 * A test executed from the test script.
 */
export interface TestReportTest extends BackboneElement {
  /**
   * An action should contain either an operation or an assertion but not both.  It can contain any number of variables.
   */
  action: TestReportTestAction[];
  /**
   * A short description of the test used by test engines for tracking and reporting purposes.
   */
  description?: string;
  _description?: Element;
  /**
   * The name of this test used for tracking/logging purposes by test engines.
   */
  name?: string;
  _name?: Element;
}
/**
 * An action should contain either an operation or an assertion but not both.  It can contain any number of variables.
 */
export interface TestReportTeardownAction extends BackboneElement {
  /**
   * An operation would involve a REST request to a server.
   */
  operation: TestReportSetupActionOperation;
}
/**
 * The results of the series of operations required to clean up after all the tests were executed (successfully or otherwise).
 */
export interface TestReportTeardown extends BackboneElement {
  /**
   * An action should contain either an operation or an assertion but not both.  It can contain any number of variables.
   */
  action: TestReportTeardownAction[];
}
/**
 * A summary of information based on the results of executing a TestScript.
 */
export interface TestReport extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'TestReport';
  /**
   * Identifier for the TestScript assigned for external purposes outside the context of FHIR.
   */
  identifier?: Identifier;
  /**
   * Additional specific dates may be added as extensions.
   */
  issued?: string;
  _issued?: Element;
  /**
   * Not expected to be globally unique.
   */
  name?: string;
  _name?: Element;
  /**
   * A participant in the test execution, either the execution engine, a client, or a server.
   */
  participant?: TestReportParticipant[];
  /**
   * The pass and fail result represents a completed test script execution. The pending result represents a test script execution that has not yet started or is currently in progress.
   */
  result: string;
  _result?: Element;
  /**
   * The final score (percentage of tests passed) resulting from the execution of the TestScript.
   */
  score?: number;
  /**
   * The results of the series of required setup operations before the tests were executed.
   */
  setup?: TestReportSetup;
  /**
   * The status represents where the execution is currently within the test script execution life cycle.
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
  /**
   * The results of the series of operations required to clean up after all the tests were executed (successfully or otherwise).
   */
  teardown?: TestReportTeardown;
  /**
   * A test executed from the test script.
   */
  test?: TestReportTest[];
  /**
   * Usually an organization, but may be an individual. This item SHOULD be populated unless the information is available from context.
   */
  tester?: string;
  _tester?: Element;
  /**
   * Ideally this is an absolute URL that is used to identify the version-specific TestScript that was executed, matching the `TestScript.url`.
   */
  testScript: Reference;
}
/**
 * Code Values for the TestReport.result field
 */
export enum TestReportResultCodes {
  PASS = 'pass',
  FAIL = 'fail',
  PENDING = 'pending',
}
/**
 * Code Values for the TestReport.status field
 */
export enum TestReportStatusCodes {
  COMPLETED = 'completed',
  IN_PROGRESS = 'in-progress',
  WAITING = 'waiting',
  STOPPED = 'stopped',
  ENTERED_IN_ERROR = 'entered-in-error',
}
/**
 * The purpose of this element is to define the profile of an origin element used elsewhere in the script.  Test engines could then use the origin-profile mapping to offer a filtered list of test systems that can serve as the sender for the interaction.
 */
export interface TestScriptOrigin extends BackboneElement {
  /**
   * A given origin index (e.g. 1) can appear only once in the list (e.g. Origin 1 cannot be specified twice ... once as FormFiller and again as FormProcessor within the same script as that could get confusing during test configuration).
   * Different origin indices could play the same actor in the same test script (e.g. You could have two different test systems acting as Form-Filler).
   * The origin indices provided elsewhere in the test script must be one of these origin indices.
   */
  index: number;
  /**
   * Must be a "sender"/"client" profile.
   */
  profile: Coding;
}
/**
 * The purpose of this element is to define the profile of a destination element used elsewhere in the script.  Test engines could then use the destination-profile mapping to offer a filtered list of test systems that can serve as the receiver for the interaction.
 */
export interface TestScriptDestination extends BackboneElement {
  /**
   * A given destination index (e.g. 1) can appear only once in the list (e.g. Destination 1 cannot be specified twice ... once as Form-Manager and again as Form-Processor within the same script as that could get confusing during test configuration).
   * Different destination indices could play the same actor in the same test script (e.g. You could have two different test systems acting as Form-Manager).
   * The destination indices provided elsewhere in the test script must be one of these destination indices.
   */
  index: number;
  /**
   * Must be a "receiver"/"server" profile.
   */
  profile: Coding;
}
/**
 * A link to the FHIR specification that this test is covering.
 */
export interface TestScriptMetadataLink extends BackboneElement {
  /**
   * Short description of the link.
   */
  description?: string;
  _description?: Element;
  /**
   * URL to a particular requirement or feature within the FHIR specification.
   */
  url: string;
  _url?: Element;
}
/**
 * When the metadata capabilities section is defined at TestScript.metadata or at TestScript.setup.metadata, and the server's conformance statement does not contain the elements defined in the minimal conformance statement, then all the tests in the TestScript are skipped.  When the metadata capabilities section is defined at TestScript.test.metadata and the server's conformance statement does not contain the elements defined in the minimal conformance statement, then only that test is skipped.  The "metadata.capabilities.required" and "metadata.capabilities.validated" elements only indicate whether the capabilities are the primary focus of the test script or not.  They do not impact the skipping logic.  Capabilities whose "metadata.capabilities.validated" flag is true are the primary focus of the test script.
 */
export interface TestScriptMetadataCapability extends BackboneElement {
  /**
   * The conformance statement of the server has to contain at a minimum the contents of the reference pointed to by this element.
   */
  capabilities: string;
  _capabilities?: Element;
  /**
   * Description of the capabilities that this test script is requiring the server to support.
   */
  description?: string;
  _description?: Element;
  /**
   * Which server these requirements apply to.
   */
  destination?: number;
  /**
   * Links to the FHIR specification that describes this interaction and the resources involved in more detail.
   */
  link?: string[];
  _link?: Element;
  /**
   * Which origin server these requirements apply to.
   */
  origin?: number[];
  /**
   * Whether or not the test execution will require the given capabilities of the server in order for this test script to execute.
   */
  required: boolean;
  _required?: Element;
  /**
   * Whether or not the test execution will validate the given capabilities of the server in order for this test script to execute.
   */
  validated: boolean;
  _validated?: Element;
}
/**
 * The required capability must exist and are assumed to function correctly on the FHIR server being tested.
 */
export interface TestScriptMetadata extends BackboneElement {
  /**
   * When the metadata capabilities section is defined at TestScript.metadata or at TestScript.setup.metadata, and the server's conformance statement does not contain the elements defined in the minimal conformance statement, then all the tests in the TestScript are skipped.  When the metadata capabilities section is defined at TestScript.test.metadata and the server's conformance statement does not contain the elements defined in the minimal conformance statement, then only that test is skipped.  The "metadata.capabilities.required" and "metadata.capabilities.validated" elements only indicate whether the capabilities are the primary focus of the test script or not.  They do not impact the skipping logic.  Capabilities whose "metadata.capabilities.validated" flag is true are the primary focus of the test script.
   */
  capability: TestScriptMetadataCapability[];
  /**
   * A link to the FHIR specification that this test is covering.
   */
  link?: TestScriptMetadataLink[];
}
/**
 * Fixture in the test script - by reference (uri). All fixtures are required for the test script to execute.
 */
export interface TestScriptFixture extends BackboneElement {
  /**
   * Whether or not to implicitly create the fixture during setup. If true, the fixture is automatically created on each server being tested during setup, therefore no create operation is required for this fixture in the TestScript.setup section.
   */
  autocreate: boolean;
  _autocreate?: Element;
  /**
   * Whether or not to implicitly delete the fixture during teardown. If true, the fixture is automatically deleted on each server being tested during teardown, therefore no delete operation is required for this fixture in the TestScript.teardown section.
   */
  autodelete: boolean;
  _autodelete?: Element;
  /**
   * See http://build.fhir.org/resourcelist.html for complete list of resource types.
   */
  resource?: Reference;
}
/**
 * Variables would be set based either on XPath/JSONPath expressions against fixtures (static and response), or headerField evaluations against response headers. If variable evaluates to nodelist or anything other than a primitive value, then test engine would report an error.  Variables would be used to perform clean replacements in "operation.params", "operation.requestHeader.value", and "operation.url" element values during operation calls and in "assert.value" during assertion evaluations. This limits the places that test engines would need to look for placeholders "${}".  Variables are scoped to the whole script. They are NOT evaluated at declaration. They are evaluated by test engine when used for substitutions in "operation.params", "operation.requestHeader.value", and "operation.url" element values during operation calls and in "assert.value" during assertion evaluations.  See example testscript-search.xml.
 */
export interface TestScriptVariable extends BackboneElement {
  /**
   * The purpose of this element is to allow for a pre-defined value that can be used as a default or as an override value. Test engines can optionally use this as a placeholder for user-defined execution time values.
   */
  defaultValue?: string;
  _defaultValue?: Element;
  /**
   * A free text natural language description of the variable and its purpose.
   */
  description?: string;
  _description?: Element;
  /**
   * If headerField is defined, then the variable will be evaluated against the headers that sourceId is pointing to.  If expression or path is defined, then the variable will be evaluated against the fixture body that sourceId is pointing to.  It is an error to define any combination of expression, headerField and path.
   */
  expression?: string;
  _expression?: Element;
  /**
   * If headerField is defined, then the variable will be evaluated against the headers that sourceId is pointing to.  If path is defined, then the variable will be evaluated against the fixture body that sourceId is pointing to.  It is an error to define both headerField and path.
   */
  headerField?: string;
  _headerField?: Element;
  /**
   * Displayable text string with hint help information to the user when entering a default value.
   */
  hint?: string;
  _hint?: Element;
  /**
   * Placeholders would contain the variable name wrapped in ${} in "operation.params", "operation.requestHeader.value", and "operation.url" elements.  These placeholders would need to be replaced by the variable value before the operation is executed.
   */
  name: string;
  _name?: Element;
  /**
   * If headerField is defined, then the variable will be evaluated against the headers that sourceId is pointing to.  If expression or path is defined, then the variable will be evaluated against the fixture body that sourceId is pointing to.  It is an error to define any combination of expression, headerField and path.
   */
  path?: string;
  _path?: Element;
  /**
   * This can be a statically defined fixture (at the top of the TestScript) or a dynamically set fixture created by responseId of the `action.operation` element.
   */
  sourceId?: string;
  _sourceId?: Element;
}
/**
 * This gives control to test-script writers to set headers explicitly based on test requirements.  It will allow for testing using:  - "If-Modified-Since" and "If-None-Match" headers.  See http://build.fhir.org/http.html#2.1.0.5.1 - "If-Match" header.  See http://build.fhir.org/http.html#2.1.0.11 - Conditional Create using "If-None-Exist".  See http://build.fhir.org/http.html#2.1.0.13.1 - Invalid "Content-Type" header for negative testing. - etc.
 */
export interface TestScriptSetupActionOperationRequestHeader extends BackboneElement {
  /**
   * If header element is specified, then field is required.
   */
  field: string;
  _field?: Element;
  /**
   * If header element is specified, then value is required.  No conversions will be done by the test engine e.g. "xml" to "application/fhir+xml".  The values will be set in HTTP headers "as-is".  Test engines do have to look for placeholders (${}) and replace the variable placeholders with the variable values at runtime before sending the request.
   */
  value: string;
  _value?: Element;
}
/**
 * The operation to perform.
 */
export interface TestScriptSetupActionOperation extends BackboneElement {
  /**
   * If this is specified, then test engine shall set the 'Accept' header to the corresponding value.  If you'd like to explicitly set the 'Accept' to some other value then use the 'requestHeader' element.
   */
  accept?: string;
  _accept?: Element;
  /**
   * If this is specified, then test engine shall set the 'Content-Type' header to the corresponding value.  If you'd like to explicitly set the 'Content-Type' to some other value then use the 'requestHeader' element.
   */
  contentType?: string;
  _contentType?: Element;
  /**
   * This has no impact on the verification itself.
   */
  description?: string;
  _description?: Element;
  /**
   * If multiple TestScript.destination elements are defined and operation.destination is undefined, test engine will report an error as it cannot determine what destination to use for the exchange.
   */
  destination?: number;
  /**
   * Whether or not to implicitly send the request url in encoded format. The default is true to match the standard RESTful client behavior. Set to false when communicating with a server that does not support encoded url paths.
   */
  encodeRequestUrl: boolean;
  _encodeRequestUrl?: Element;
  /**
   * This has no impact on the verification itself.
   */
  label?: string;
  _label?: Element;
  /**
   * The primary purpose of the explicit HTTP method is support of  HTTP POST method invocation of the FHIR search. Other uses will include support of negative testing.
   */
  method?: string;
  _method?: Element;
  /**
   * If absent, test engine will send the message.  When present, test engine will not send the request message but will wait for the request message to be sent from this origin server.
   */
  origin?: number;
  /**
   * If "url" element is specified, then "targetId", "params", and "resource" elements will be ignored as "url" element will have everything needed for constructing the request url.  If "params" element is specified, then "targetId" element is ignored.  For FHIR operations that require a resource (e.g. "read" and "vread" operations), the "resource" element must be specified when "params" element is specified.  If "url" and "params" elements are absent, then the request url will be constructed from "targetId" fixture if present.  For "read" operation, the resource and id values will be extracted from "targetId" fixture and used to construct the url.  For "vread" and "history" operations, the versionId value will also be used.   Test engines would append whatever is specified for "params" to the URL after the resource type without tampering with the string (beyond encoding the URL for HTTP).  The "params" element does not correspond exactly to "search parameters".  Nor is it the "path".  It corresponds to the part of the URL that comes after the [type] (when "resource" element is specified); e.g. It corresponds to "/[id]/_history/[vid] {?_format=[mime-type]}" in the following operation: GET [base]/[type]/[id]/_history/[vid] {?_format=[mime-type]}  Test engines do have to look for placeholders (${}) and replace the variable placeholders with the variable values at runtime before sending the request.
   */
  params?: string;
  _params?: Element;
  /**
   * This gives control to test-script writers to set headers explicitly based on test requirements.  It will allow for testing using:  - "If-Modified-Since" and "If-None-Match" headers.  See http://build.fhir.org/http.html#2.1.0.5.1 - "If-Match" header.  See http://build.fhir.org/http.html#2.1.0.11 - Conditional Create using "If-None-Exist".  See http://build.fhir.org/http.html#2.1.0.13.1 - Invalid "Content-Type" header for negative testing. - etc.
   */
  requestHeader?: TestScriptSetupActionOperationRequestHeader[];
  /**
   * If a requestId is supplied, then the resulting request (both headers and body) is mapped to the fixture ID (which may be entirely new and previously undeclared) designated by "requestId".  If requestId is not specified, it is the test engine's responsibility to store the request and use it as the requestId in subsequent assertions when assertion path and/or headerField is specified, direction is equal to request, and the requestId in not specified.
   */
  requestId?: string;
  _requestId?: Element;
  /**
   * If "url" element is specified, then "targetId", "params", and "resource" elements will be ignored as "url" element will have everything needed for constructing the request url.  If "params" element is specified, then "targetId" element is ignored. For FHIR operations that require a resource (e.g. "read" and "vread" operations), the "resource" element must be specified when "params" element is specified.  If "url" and "params" elements are absent, then the request url will be constructed from "targetId" fixture if present. For "read" operation, the resource and id values will be extracted from "targetId" fixture and used to construct the url. For "vread" and "history" operations, the versionId value will also be used.
   */
  resource?: string;
  _resource?: Element;
  /**
   * If a responseId is supplied, and the server responds, then the resulting response (both headers and body) is mapped to the fixture ID (which may be entirely new and previously undeclared) designated by "responseId".  If responseId is not specified, it is the test engine's responsibility to store the response and use it as the responseId in subsequent assertions when assertion path and/or headerField is specified and the responseId is not specified.
   */
  responseId?: string;
  _responseId?: Element;
  /**
   * The id of the fixture used as the body of a PUT or POST request.
   */
  sourceId?: string;
  _sourceId?: Element;
  /**
   * If "url" element is specified, then "targetId", "params", and "resource" elements will be ignored as "url" element will have everything needed for constructing the request url.  If "params" element is specified, then "targetId" element is ignored.  For FHIR operations that require a resource (e.g. "read" and "vread" operations), the "resource" element must be specified when "params" element is specified.  If "url" and "params" elements are absent, then the request url will be constructed from "targetId" fixture if present.  For "read" operation, the resource and id values will be extracted from "targetId" fixture and used to construct the url.  For "vread" and "history" operations, the versionId value will also be used.
   */
  targetId?: string;
  _targetId?: Element;
  /**
   * See http://build.fhir.org/http.html for list of server interactions.
   */
  type?: Coding;
  /**
   * Used to set the request URL explicitly.  If "url" element is defined, then "targetId", "resource", and "params" elements will be ignored.  Test engines would use whatever is specified in "url" without tampering with the string (beyond encoding the URL for HTTP).  Test engines do have to look for placeholders (${}) and replace the variable placeholders with the variable values at runtime before sending the request.
   */
  url?: string;
  _url?: Element;
}
/**
 * Code Values for the TestScript.setup.action.operation.method field
 */
export enum TestScriptSetupActionOperationMethodCodes {
  DELETE = 'delete',
  GET = 'get',
  OPTIONS = 'options',
  PATCH = 'patch',
  POST = 'post',
  PUT = 'put',
  HEAD = 'head',
}
/**
 * In order to evaluate an assertion, the request, response, and results of the most recently executed operation must always be maintained by the test engine.
 */
export interface TestScriptSetupActionAssert extends BackboneElement {
  /**
   * Thefhirpath expression to be evaluated against the expected fixture to compare to. Ignored if "assert.value" is used. The evaluation will be done before the assertion is evaluated.
   */
  compareToSourceExpression?: string;
  _compareToSourceExpression?: Element;
  /**
   * Id of the source fixture used as the contents to be evaluated by either the "source/expression" or "sourceId/path" definition.
   */
  compareToSourceId?: string;
  _compareToSourceId?: Element;
  /**
   * The XPath or JSONPath expression to be evaluated against the expected fixture to compare to. Ignored if "assert.value" is used. The evaluation will be done before the assertion is evaluated.
   */
  compareToSourcePath?: string;
  _compareToSourcePath?: Element;
  /**
   * If this is specified, then test engine shall confirm that the content-type of the last operation's headers is set to this value.  If "assert.sourceId" element is specified, then the evaluation will be done against the headers mapped to that sourceId (and not the last operation's headers).  If you'd like to have more control over the string, then use 'assert.headerField' instead.
   */
  contentType?: string;
  _contentType?: Element;
  /**
   * This has no impact on the verification itself.
   */
  description?: string;
  _description?: Element;
  /**
   * If the direction is specified as "response" (the default), then the processing of this assert is against the received response message. If the direction is specified as "request", then the processing of this assert is against the sent request message.
   */
  direction?: string;
  _direction?: Element;
  /**
   * If both "expression" and a "fixtureId" are specified, then the expression will be evaluated against the request or response body mapped to the fixtureId.  If "expression" is specified and a "fixtureId" is not, then the expression will be evaluated against the response body of the last operation.  Test engines are to store the request and response body and headers of the last operation at all times for subsequent assertions.
   */
  expression?: string;
  _expression?: Element;
  /**
   * If "headerField" is specified then "value" must be specified.  If "sourceId" is not specified, then "headerField" will be evaluated against the last operation's response headers.  Test engines are to keep track of the last operation's response body and response headers.
   */
  headerField?: string;
  _headerField?: Element;
  /**
   * This has no impact on the verification itself.
   */
  label?: string;
  _label?: Element;
  /**
   * Asserts that the response contains all the element/content in another fixture pointed to by minimumId.  This can be a statically defined fixture or one that is dynamically set via responseId.
   */
  minimumId?: string;
  _minimumId?: Element;
  /**
   * Asserts that the Bundle contains first, last, and next links.
   */
  navigationLinks?: boolean;
  _navigationLinks?: Element;
  /**
   * Operators are useful especially for negative testing.  If operator is not specified, then the "equals" operator is assumed; e.g. ```<code>   <assert>  <operator value="in" />  <responseCode value="200,201,204" />    </assert>    <assert>  <operator value="notEquals" />  <response value="okay"/>   </assert>    <assert>  <operator value="greaterThan" />    <responseHeader>     <field value="Content-Length" />     <value value="0" />    </responseHeader/>   </assert> </code> ```.
   */
  operator?: string;
  _operator?: Element;
  /**
   * If both "path" and a "fixtureId" are specified, then the path will be evaluated against the request or response body mapped to the fixtureId.  If "path" is specified and a "fixtureId" is not, then the path will be evaluated against the response body of the last operation.  Test engines are to store the request and response body and headers of the last operation at all times for subsequent assertions.
   */
  path?: string;
  _path?: Element;
  /**
   * If "requestMethod" is specified then it will be used in place of "value". The "requestMethod" will evaluate against the last operation's request HTTP operation.
   */
  requestMethod?: string;
  _requestMethod?: Element;
  /**
   * If "requestURL" is specified then it will be used in place of "value". The "requestURL" will evaluate against the last operation's full request URL path string.
   */
  requestURL?: string;
  _requestURL?: Element;
  /**
   * This will be expected resource type in response body e.g. in read, vread, search, etc.  See http://build.fhir.org/resourcelist.html for complete list of resource types; e.g. <assert > <resourceType value="Patient" </assert>.
   */
  resource?: string;
  _resource?: Element;
  /**
   * This is a shorter way of achieving similar verifications via "assert.responseCode".  If you need more control, then use "assert.responseCode"  e.g. <assert>  <contentType value="json" />  <response value="okay"/> </assert>.
   */
  response?: string;
  _response?: Element;
  /**
   * To be used with "operator" attribute value. Asserts that the response code equals this value if "operator" is not specified.   If the operator is "in" or "notIn" then the responseCode would be a comma-separated list of values e.g. "200,201". Otherwise, it's expected to be a numeric value.   If "fixture" is not specified, then the "responseBodyId" value of the last operation is assumed.
   */
  responseCode?: string;
  _responseCode?: Element;
  /**
   * This can be a statically defined fixture (at the top of the testscript) or a dynamically set fixture created by responseId of the action.operation element.
   */
  sourceId?: string;
  _sourceId?: Element;
  /**
   * The ID of a Profile fixture. Asserts that the response is valid according to the Profile specified by validateProfileId.
   */
  validateProfileId?: string;
  _validateProfileId?: Element;
  /**
   * The string-representation of a number, string, or boolean that is expected.  Test engines do have to look for placeholders (${}) and replace the variable placeholders with the variable values at runtime before comparing this value to the actual value.
   */
  value?: string;
  _value?: Element;
  /**
   * If this element is specified and it is true, then assertion failures can be logged by test engine but should not stop the test script execution from proceeding.  There are likely cases where the spec is not clear on what should happen. If the spec says something is optional (maybe a response header for example), but a server doesn’t do it, we could choose to issue a warning.
   */
  warningOnly: boolean;
  _warningOnly?: Element;
}
/**
 * Code Values for the TestScript.setup.action.assert.direction field
 */
export enum TestScriptSetupActionAssertDirectionCodes {
  RESPONSE = 'response',
  REQUEST = 'request',
}
/**
 * Code Values for the TestScript.setup.action.assert.operator field
 */
export enum TestScriptSetupActionAssertOperatorCodes {
  EQUALS = 'equals',
  NOTEQUALS = 'notEquals',
  IN = 'in',
  NOTIN = 'notIn',
  GREATERTHAN = 'greaterThan',
  LESSTHAN = 'lessThan',
  EMPTY = 'empty',
  NOTEMPTY = 'notEmpty',
  CONTAINS = 'contains',
  NOTCONTAINS = 'notContains',
  EVAL = 'eval',
}
/**
 * Code Values for the TestScript.setup.action.assert.requestMethod field
 */
export enum TestScriptSetupActionAssertRequestMethodCodes {
  DELETE = 'delete',
  GET = 'get',
  OPTIONS = 'options',
  PATCH = 'patch',
  POST = 'post',
  PUT = 'put',
  HEAD = 'head',
}
/**
 * Code Values for the TestScript.setup.action.assert.response field
 */
export enum TestScriptSetupActionAssertResponseCodes {
  OKAY = 'okay',
  CREATED = 'created',
  NOCONTENT = 'noContent',
  NOTMODIFIED = 'notModified',
  BAD = 'bad',
  FORBIDDEN = 'forbidden',
  NOTFOUND = 'notFound',
  METHODNOTALLOWED = 'methodNotAllowed',
  CONFLICT = 'conflict',
  GONE = 'gone',
  PRECONDITIONFAILED = 'preconditionFailed',
  UNPROCESSABLE = 'unprocessable',
}
/**
 * An action should contain either an operation or an assertion but not both.  It can contain any number of variables.
 */
export interface TestScriptSetupAction extends BackboneElement {
  /**
   * In order to evaluate an assertion, the request, response, and results of the most recently executed operation must always be maintained by the test engine.
   */
  assert?: TestScriptSetupActionAssert;
  /**
   * The operation to perform.
   */
  operation?: TestScriptSetupActionOperation;
}
/**
 * A series of required setup operations before tests are executed.
 */
export interface TestScriptSetup extends BackboneElement {
  /**
   * An action should contain either an operation or an assertion but not both.  It can contain any number of variables.
   */
  action: TestScriptSetupAction[];
}
/**
 * An action should contain either an operation or an assertion but not both.  It can contain any number of variables.
 */
export interface TestScriptTestAction extends BackboneElement {
  /**
   * In order to evaluate an assertion, the request, response, and results of the most recently executed operation must always be maintained by the test engine.
   */
  assert?: TestScriptSetupActionAssert;
  /**
   * An operation would involve a REST request to a server.
   */
  operation?: TestScriptSetupActionOperation;
}
/**
 * A test in this script.
 */
export interface TestScriptTest extends BackboneElement {
  /**
   * An action should contain either an operation or an assertion but not both.  It can contain any number of variables.
   */
  action: TestScriptTestAction[];
  /**
   * A short description of the test used by test engines for tracking and reporting purposes.
   */
  description?: string;
  _description?: Element;
  /**
   * The name of this test used for tracking/logging purposes by test engines.
   */
  name?: string;
  _name?: Element;
}
/**
 * An action should contain either an operation or an assertion but not both.  It can contain any number of variables.
 */
export interface TestScriptTeardownAction extends BackboneElement {
  /**
   * An operation would involve a REST request to a server.
   */
  operation: TestScriptSetupActionOperation;
}
/**
 * A series of operations required to clean up after all the tests are executed (successfully or otherwise).
 */
export interface TestScriptTeardown extends BackboneElement {
  /**
   * An action should contain either an operation or an assertion but not both.  It can contain any number of variables.
   */
  action: TestScriptTeardownAction[];
}
/**
 * A structured set of tests against a FHIR server or client implementation to determine compliance against the FHIR specification.
 */
export interface TestScript extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'TestScript';
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * A copyright statement relating to the test script and/or its contents. Copyright statements are generally legal restrictions on the use and publishing of the test script.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the test script. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the test script was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the test script as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the test script is presumed to be the predominant language in the place the test script was created).
   */
  description?: string;
  _description?: Element;
  /**
   * The purpose of this element is to define the profile of a destination element used elsewhere in the script.  Test engines could then use the destination-profile mapping to offer a filtered list of test systems that can serve as the receiver for the interaction.
   */
  destination?: TestScriptDestination[];
  /**
   * Allows filtering of test scripts that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Fixture in the test script - by reference (uri). All fixtures are required for the test script to execute.
   */
  fixture?: TestScriptFixture[];
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this test script outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier;
  /**
   * It may be possible for the test script to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * The required capability must exist and are assumed to function correctly on the FHIR server being tested.
   */
  metadata?: TestScriptMetadata;
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.
   */
  name: string;
  _name?: Element;
  /**
   * The purpose of this element is to define the profile of an origin element used elsewhere in the script.  Test engines could then use the origin-profile mapping to offer a filtered list of test systems that can serve as the sender for the interaction.
   */
  origin?: TestScriptOrigin[];
  /**
   * See http://build.fhir.org/resourcelist.html for complete list of resource types.
   */
  profile?: Reference[];
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the test script is the organization or individual primarily responsible for the maintenance and upkeep of the test script. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the test script. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the test script. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this test script.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * A series of required setup operations before tests are executed.
   */
  setup?: TestScriptSetup;
  /**
   * Allows filtering of test scripts that are appropriate for use versus not.
   */
  status: string;
  _status?: Element;
  /**
   * A series of operations required to clean up after all the tests are executed (successfully or otherwise).
   */
  teardown?: TestScriptTeardown;
  /**
   * A test in this script.
   */
  test?: TestScriptTest[];
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * Variables would be set based either on XPath/JSONPath expressions against fixtures (static and response), or headerField evaluations against response headers. If variable evaluates to nodelist or anything other than a primitive value, then test engine would report an error.  Variables would be used to perform clean replacements in "operation.params", "operation.requestHeader.value", and "operation.url" element values during operation calls and in "assert.value" during assertion evaluations. This limits the places that test engines would need to look for placeholders "${}".  Variables are scoped to the whole script. They are NOT evaluated at declaration. They are evaluated by test engine when used for substitutions in "operation.params", "operation.requestHeader.value", and "operation.url" element values during operation calls and in "assert.value" during assertion evaluations.  See example testscript-search.xml.
   */
  variable?: TestScriptVariable[];
  /**
   * There may be different test script instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the test script with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the TestScript.status field
 */
export enum TestScriptStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Concepts have both a ```display``` and an array of ```designation```. The display is equivalent to a special designation with an implied ```designation.use``` of "primary code" and a language equal to the [Resource Language](resource.html#language).
 */
export interface ValueSetComposeIncludeConceptDesignation extends BackboneElement {
  /**
   * In the absence of a language, the resource language applies.
   */
  language?: string;
  _language?: Element;
  /**
   * If no use is provided, the designation can be assumed to be suitable for general display to a human user.
   */
  use?: Coding;
  /**
   * The text value for this designation.
   */
  value: string;
  _value?: Element;
}
/**
 * The list of concepts is considered ordered, though the order might not have any particular significance. Typically, the order of an expansion follows that defined in the compose element.
 */
export interface ValueSetComposeIncludeConcept extends BackboneElement {
  /**
   * Expressions are allowed if defined by the underlying code system.
   */
  code: string;
  _code?: Element;
  /**
   * Concepts have both a ```display``` and an array of ```designation```. The display is equivalent to a special designation with an implied ```designation.use``` of "primary code" and a language equal to the [Resource Language](resource.html#language).
   */
  designation?: ValueSetComposeIncludeConceptDesignation[];
  /**
   * The value set resource allows for an alternative display to be specified for when this concept is used in this particular value set. See notes in the value set narrative about the correct use of this element.
   */
  display?: string;
  _display?: Element;
}
/**
 * Selecting codes by specifying filters based on properties is only possible where the underlying code system defines appropriate properties. Note that in some cases, the underlying code system defines the logical concepts but not the literal codes for the concepts. In such cases, the literal definitions may be provided by a third party.
 */
export interface ValueSetComposeIncludeFilter extends BackboneElement {
  /**
   * In case filter.property represents a property of the system, the operation applies to the selected property. In case filter.property represents a filter of the system, the operation SHALL match one of the CodeSystem.filter.operator values.
   */
  op: string;
  _op?: Element;
  /**
   * A code that identifies a property or a filter defined in the code system.
   */
  property: string;
  _property?: Element;
  /**
   * Use regex matching with care - full regex matching on every SNOMED CT term is prohibitive, for example.
   */
  value: string;
  _value?: Element;
}
/**
 * Code Values for the ValueSet.compose.include.filter.op field
 */
export enum ValueSetComposeIncludeFilterOpCodes {
  EQUALS = '=',
  IS_A = 'is-a',
  DESCENDENT_OF = 'descendent-of',
  IS_NOT_A = 'is-not-a',
  REGEX = 'regex',
  IN = 'in',
  NOT_IN = 'not-in',
  GENERALIZES = 'generalizes',
  EXISTS = 'exists',
}
/**
 * All the conditions in an include must be true. If a system is listed, all the codes from the system are listed. If one or more filters are listed, all of the filters must apply. If one or more value sets are listed, the codes must be in all the value sets. E.g. each include is 'include all the codes that meet all these conditions'.
 */
export interface ValueSetComposeInclude extends BackboneElement {
  /**
   * The list of concepts is considered ordered, though the order might not have any particular significance. Typically, the order of an expansion follows that defined in the compose element.
   */
  concept?: ValueSetComposeIncludeConcept[];
  /**
   * Selecting codes by specifying filters based on properties is only possible where the underlying code system defines appropriate properties. Note that in some cases, the underlying code system defines the logical concepts but not the literal codes for the concepts. In such cases, the literal definitions may be provided by a third party.
   */
  filter?: ValueSetComposeIncludeFilter[];
  /**
   * If there are no codes or filters, the entire code system is included. Note that the set of codes that are included may contain abstract codes. See ''Coding.system'' for further documentation about the correct value for the system element.
   */
  system?: string;
  _system?: Element;
  /**
   * The value set URI is either a logical reference to a defined value set such as a [SNOMED CT reference set](snomedct.html#implicit), or a direct reference to a value set definition using ValueSet.url. The reference might not refer to an actual FHIR ValueSet resource; in this case, whatever is referred to is an implicit definition of a value set that needs to be clear about how versions are resolved.
   */
  valueSet?: string[];
  _valueSet?: Element;
  /**
   * This is used when selecting the descendants of a concept - they may change between versions. If no version is specified, then the exact contents of the value set might not be known until a context of use binds it to a particular version. The special value '*' means all versions; It is at server discretion regarding expansions and which versions must be supported.
   */
  version?: string;
  _version?: Element;
}
/**
 * A set of criteria that define the contents of the value set by including or excluding codes selected from the specified code system(s) that the value set draws from. This is also known as the Content Logical Definition (CLD).
 */
export interface ValueSetCompose extends BackboneElement {
  /**
   * Usually this is used to selectively exclude codes that were included by subsumption in the inclusions. Any display names specified for the codes are ignored.
   */
  exclude?: ValueSetComposeInclude[];
  /**
   * Note that in the FHIR terminology framework, "deprecated" does not mean inactive, but in some code systems, e.g. LOINC, "deprecated" does mean inactive. Code systems should define what codes are considered to be inactive. If this is not clearly defined (including in the FHIR code system resource), then all codes are assumed to be active.
   * The Value Set Definition specification defines an ActiveOnly element, which is the reverse of this element e.g. (ValueSet.compose.inactive=FALSE) is the same as (VSD.ActiveOnly=TRUE).
   */
  inactive?: boolean;
  _inactive?: Element;
  /**
   * All the conditions in an include must be true. If a system is listed, all the codes from the system are listed. If one or more filters are listed, all of the filters must apply. If one or more value sets are listed, the codes must be in all the value sets. E.g. each include is 'include all the codes that meet all these conditions'.
   */
  include: ValueSetComposeInclude[];
  /**
   * With a defined lockedDate the value set is considered "Locked". Otherwise, the value set may have different expansions as underlying code systems and/or value sets evolve.  The interpretation of lockedDate is often dependent on the context - e.g. a SNOMED CT derived value set with a lockedDate will have a different expansion in USA than in UK.  If a value set specifies a version for include and exclude statements, and also specifies a locked date, the specified versions need to be available that date, or the value set will not be usable.
   */
  lockedDate?: string;
  _lockedDate?: Element;
}
/**
 * The server decides which parameters to include here, but at a minimum, the list SHOULD include all of the parameters that affect the $expand operation. If the expansion will be persisted all of these parameters SHALL be included. If the codeSystem on the server has a specified version then this version SHALL be provided as a parameter in the expansion (note that not all code systems have a version).
 */
export interface ValueSetExpansionParameter extends BackboneElement {
  /**
   * The names are assigned at the discretion of the server.
   */
  name: string;
  _name?: Element;
  /**
   * The value of the parameter.
   */
  valueString?: string;
  _valueString?: Element;
  /**
   * The value of the parameter.
   */
  valueBoolean?: boolean;
  _valueBoolean?: Element;
  /**
   * The value of the parameter.
   */
  valueInteger?: number;
  /**
   * The value of the parameter.
   */
  valueDecimal?: number;
  /**
   * The value of the parameter.
   */
  valueUri?: string;
  _valueUri?: Element;
  /**
   * The value of the parameter.
   */
  valueCode?: string;
  _valueCode?: Element;
  /**
   * The value of the parameter.
   */
  valueDateTime?: string;
  _valueDateTime?: Element;
}
/**
 * The codes that are contained in the value set expansion.
 */
export interface ValueSetExpansionContains extends BackboneElement {
  /**
   * This should not be understood to exclude its use for searching (e.g. by subsumption testing). The client should know whether it is appropriate for the user to select an abstract code or not.
   */
  abstract?: boolean;
  _abstract?: Element;
  /**
   * The code for this item in the expansion hierarchy. If this code is missing the entry in the hierarchy is a place holder (abstract) and does not represent a valid code in the value set.
   */
  code?: string;
  _code?: Element;
  /**
   * If the expansion uses this element, there is  no implication about the logical relationship between them, and the  structure cannot be used for logical inferencing. The structure  exists to provide navigational assistance for helping human users to  locate codes in the expansion.
   */
  contains?: ValueSetExpansionContains[];
  /**
   * The designations provided must be based on the value set and code system definitions.
   */
  designation?: ValueSetComposeIncludeConceptDesignation[];
  /**
   * The recommended display for this item in the expansion.
   */
  display?: string;
  _display?: Element;
  /**
   * This should only have a value if the concept is inactive.
   */
  inactive?: boolean;
  _inactive?: Element;
  /**
   * An absolute URI which is the code system in which the code for this item in the expansion is defined.
   */
  system?: string;
  _system?: Element;
  /**
   * The exact value of the version string is specified by the system from which the code is derived.
   */
  version?: string;
  _version?: Element;
}
/**
 * Expansion is performed to produce a collection of codes that are ready to use for data entry or validation. Value set expansions are always considered to be stateless - they are a record of the set of codes in the value set at a point in time under a given set of conditions, and are not subject to ongoing maintenance.
 * Expansion.parameter is  a simplified list of parameters - a subset of the features of the [Parameters](parameters.html) resource.
 */
export interface ValueSetExpansion extends BackboneElement {
  /**
   * The codes that are contained in the value set expansion.
   */
  contains?: ValueSetExpansionContains[];
  /**
   * Typically, this uri is a UUID (e.g. urn:uuid:8230ff20-c97a-4167-a59d-dc2cb9df16dd).
   */
  identifier?: string;
  _identifier?: Element;
  /**
   * Paging only applies to flat expansions. If a filter is applied, the count is the number of concepts that matched the filter, not the number of concepts in an unfiltered view of the expansion.
   */
  offset?: number;
  /**
   * The server decides which parameters to include here, but at a minimum, the list SHOULD include all of the parameters that affect the $expand operation. If the expansion will be persisted all of these parameters SHALL be included. If the codeSystem on the server has a specified version then this version SHALL be provided as a parameter in the expansion (note that not all code systems have a version).
   */
  parameter?: ValueSetExpansionParameter[];
  /**
   * This SHOULD be a fully populated instant, but in some circumstances, value sets are expanded by hand, and the expansion is published without that precision.
   */
  timestamp: string;
  _timestamp?: Element;
  /**
   * Paging only applies to flat expansions.
   */
  total?: number;
}
/**
 * A ValueSet resource instance specifies a set of codes drawn from one or more code systems, intended for use in a particular context. Value sets link between [CodeSystem](codesystem.html) definitions and their use in [coded elements](terminologies.html).
 */
export interface ValueSet extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'ValueSet';
  /**
   * A set of criteria that define the contents of the value set by including or excluding codes selected from the specified code system(s) that the value set draws from. This is also known as the Content Logical Definition (CLD).
   */
  compose?: ValueSetCompose;
  /**
   * May be a web site, an email address, a telephone number, etc.
   */
  contact?: ContactDetail[];
  /**
   * nullFrequently, the copyright differs between the value set and the codes that are included. The copyright statement should clearly differentiate between these when required.
   */
  copyright?: string;
  _copyright?: Element;
  /**
   * Note that this is not the same as the resource last-modified-date, since the resource may be a secondary representation of the value set. Additional specific dates may be added as extensions or be found by consulting Provenances associated with past versions of the resource.
   */
  date?: string;
  _date?: Element;
  /**
   * This description can be used to capture details such as why the value set was built, comments about misuse, instructions for clinical use and interpretation, literature references, examples from the paper world, etc. It is not a rendering of the value set as conveyed in the 'text' field of the resource itself. This item SHOULD be populated unless the information is available from context (e.g. the language of the value set is presumed to be the predominant language in the place the value set was created).The description is not intended to describe the semantics of the Value Set - there are no intrinsic semantics separate from the codes contained in its expansion. The description should capture its intended use, which is needed for ensuring integrity for its use in models across future changes. A description should be provided unless the value set is a contained resource (e.g. an anonymous value set in a profile). Most registries will require a description.
   */
  description?: string;
  _description?: Element;
  /**
   * Expansion is performed to produce a collection of codes that are ready to use for data entry or validation. Value set expansions are always considered to be stateless - they are a record of the set of codes in the value set at a point in time under a given set of conditions, and are not subject to ongoing maintenance.
   * Expansion.parameter is  a simplified list of parameters - a subset of the features of the [Parameters](parameters.html) resource.
   */
  expansion?: ValueSetExpansion;
  /**
   * Allows filtering of value sets that are appropriate for use versus not.
   */
  experimental?: boolean;
  _experimental?: Element;
  /**
   * Typically, this is used for identifiers that can go in an HL7 V3 II (instance identifier) data type, and can then identify this value set outside of FHIR, where it is not possible to use the logical URI.
   */
  identifier?: Identifier[];
  /**
   * Normally immutability is set to 'false', which is the default assumption if it is not populated.  Note that the implication is that if this is set to 'true', there may be only one ValueSet version for this definition. Immutability tends to be set to 'true' in one of two cases: - Where the value set, by the nature of its usage, cannot change.  For example "All specializations of ACT in ActClassCode" - Where there's no safe way to express the "Purpose" such that someone else could safely make changes to the value set definition. Source workflow control must guarantee that the same URI always yields the same definition.
   */
  immutable?: boolean;
  _immutable?: Element;
  /**
   * It may be possible for the value set to be used in jurisdictions other than those for which it was originally designed or intended.
   */
  jurisdiction?: CodeableConcept[];
  /**
   * The name is not expected to be globally unique. The name should be a simple alphanumeric type name to ensure that it is machine-processing friendly.A name should be provided unless the value set is a contained resource (e.g. an anonymous value set in a profile). Most registries will require a name.
   */
  name?: string;
  _name?: Element;
  /**
   * Usually an organization but may be an individual. The publisher (or steward) of the value set is the organization or individual primarily responsible for the maintenance and upkeep of the value set. This is not necessarily the same individual or organization that developed and initially authored the content. The publisher is the primary point of contact for questions or issues with the value set. This item SHOULD be populated unless the information is available from context.
   */
  publisher?: string;
  _publisher?: Element;
  /**
   * This element does not describe the usage of the value set. Instead, it provides traceability of ''why'' the resource is either needed or ''why'' it is defined as it is.  This may be used to point to source materials or specifications that drove the structure of this value set.
   */
  purpose?: string;
  _purpose?: Element;
  /**
   * Allows filtering of value sets that are appropriate for use versus not.See also the [valueset-workflowStatus](extension-valueset-workflowstatus.html) extension for additional status information related to the editorial process.
   */
  status: string;
  _status?: Element;
  /**
   * This name does not need to be machine-processing friendly and may contain punctuation, white-space, etc.
   */
  title?: string;
  _title?: Element;
  /**
   * Can be a urn:uuid: or a urn:oid: but real http: addresses are preferred.  Multiple instances may share the same URL if they have a distinct version.
   * The determination of when to create a new version of a resource (same url, new version) vs. defining a new artifact is up to the author.  Considerations for making this decision are found in [Technical and Business Versions](resource.html#versions).
   * In some cases, the resource can no longer be found at the stated url, but the url itself cannot change. Implementations can use the [meta.source](resource.html#meta) element to indicate where the current master source of the resource can be found.
   */
  url?: string;
  _url?: Element;
  /**
   * When multiple useContexts are specified, there is no expectation that all or any of the contexts apply.
   */
  useContext?: UsageContext[];
  /**
   * There may be different value set instances that have the same identifier but different versions.  The version can be appended to the url in a reference to allow a reference to a particular business version of the value set with the format [url]|[version].
   */
  version?: string;
  _version?: Element;
}
/**
 * Code Values for the ValueSet.status field
 */
export enum ValueSetStatusCodes {
  DRAFT = 'draft',
  ACTIVE = 'active',
  RETIRED = 'retired',
  UNKNOWN = 'unknown',
}
/**
 * Information about the primary source(s) involved in validation.
 */
export interface VerificationResultPrimarySource extends BackboneElement {
  /**
   * Ability of the primary source to push updates/alerts (yes; no; undetermined).
   */
  canPushUpdates?: CodeableConcept;
  /**
   * Method for communicating with the primary source (manual; API; Push).
   */
  communicationMethod?: CodeableConcept[];
  /**
   * Type of alerts/updates the primary source can send (specific requested changes; any changes; as defined by source).
   */
  pushTypeAvailable?: CodeableConcept[];
  /**
   * Type of primary source (License Board; Primary Education; Continuing Education; Postal Service; Relationship owner; Registration Authority; legal source; issuing source; authoritative source).
   */
  type?: CodeableConcept[];
  /**
   * When the target was validated against the primary source.
   */
  validationDate?: string;
  _validationDate?: Element;
  /**
   * Status of the validation of the target against the primary source (successful; failed; unknown).
   */
  validationStatus?: CodeableConcept;
  /**
   * Reference to the primary source.
   */
  who?: Reference;
}
/**
 * Information about the entity attesting to information.
 */
export interface VerificationResultAttestation extends BackboneElement {
  /**
   * The method by which attested information was submitted/retrieved (manual; API; Push).
   */
  communicationMethod?: CodeableConcept;
  /**
   * The date the information was attested to.
   */
  date?: string;
  _date?: Element;
  /**
   * When the who is asserting on behalf of another (organization or individual).
   */
  onBehalfOf?: Reference;
  /**
   * A digital identity certificate associated with the proxy entity submitting attested information on behalf of the attestation source.
   */
  proxyIdentityCertificate?: string;
  _proxyIdentityCertificate?: Element;
  /**
   * Signed assertion by the proxy entity indicating that they have the right to submit attested information on behalf of the attestation source.
   */
  proxySignature?: Signature;
  /**
   * A digital identity certificate associated with the attestation source.
   */
  sourceIdentityCertificate?: string;
  _sourceIdentityCertificate?: Element;
  /**
   * Signed assertion by the attestation source that they have attested to the information.
   */
  sourceSignature?: Signature;
  /**
   * The individual or organization attesting to information.
   */
  who?: Reference;
}
/**
 * Information about the entity validating information.
 */
export interface VerificationResultValidator extends BackboneElement {
  /**
   * Signed assertion by the validator that they have validated the information.
   */
  attestationSignature?: Signature;
  /**
   * A digital identity certificate associated with the validator.
   */
  identityCertificate?: string;
  _identityCertificate?: Element;
  /**
   * Reference to the organization validating information.
   */
  organization: Reference;
}
/**
 * Describes validation requirements, source(s), status and dates for one or more elements.
 */
export interface VerificationResult extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'VerificationResult';
  /**
   * Information about the entity attesting to information.
   */
  attestation?: VerificationResultAttestation;
  /**
   * The result if validation fails (fatal; warning; record only; none).
   */
  failureAction?: CodeableConcept;
  /**
   * Frequency of revalidation.
   */
  frequency?: Timing;
  /**
   * The date/time validation was last completed (including failed validations).
   */
  lastPerformed?: string;
  _lastPerformed?: Element;
  /**
   * The frequency with which the target must be validated (none; initial; periodic).
   */
  need?: CodeableConcept;
  /**
   * The date when target is next validated, if appropriate.
   */
  nextScheduled?: string;
  _nextScheduled?: Element;
  /**
   * Information about the primary source(s) involved in validation.
   */
  primarySource?: VerificationResultPrimarySource[];
  /**
   * The validation status of the target (attested; validated; in process; requires revalidation; validation failed; revalidation failed).
   */
  status: string;
  _status?: Element;
  /**
   * When the validation status was updated.
   */
  statusDate?: string;
  _statusDate?: Element;
  /**
   * A resource that was validated.
   */
  target?: Reference[];
  /**
   * The fhirpath location(s) within the resource that was validated.
   */
  targetLocation?: string[];
  _targetLocation?: Element;
  /**
   * The primary process by which the target is validated (edit check; value set; primary source; multiple sources; standalone; in context).
   */
  validationProcess?: CodeableConcept[];
  /**
   * What the target is validated against (nothing; primary source; multiple sources).
   */
  validationType?: CodeableConcept;
  /**
   * Information about the entity validating information.
   */
  validator?: VerificationResultValidator[];
}
/**
 * Code Values for the VerificationResult.status field
 */
export enum VerificationResultStatusCodes {
  ATTESTED = 'attested',
  VALIDATED = 'validated',
  IN_PROCESS = 'in-process',
  REQ_REVALID = 'req-revalid',
  VAL_FAIL = 'val-fail',
  REVAL_FAIL = 'reval-fail',
}
/**
 * Allows for adjustment on two axis.
 */
export interface VisionPrescriptionLensSpecificationPrism extends BackboneElement {
  /**
   * Amount of prism to compensate for eye alignment in fractional units.
   */
  amount: number;
  /**
   * The relative base, or reference lens edge, for the prism.
   */
  base: string;
  _base?: Element;
}
/**
 * Code Values for the VisionPrescription.lensSpecification.prism.base field
 */
export enum VisionPrescriptionLensSpecificationPrismBaseCodes {
  UP = 'up',
  DOWN = 'down',
  IN = 'in',
  OUT = 'out',
}
/**
 * Contain the details of  the individual lens specifications and serves as the authorization for the fullfillment by certified professionals.
 */
export interface VisionPrescriptionLensSpecification extends BackboneElement {
  /**
   * Power adjustment for multifocal lenses measured in dioptres (0.25 units).
   */
  add?: number;
  /**
   * The limits are +180 and -180 degrees.
   */
  axis?: number;
  /**
   * Back curvature measured in millimetres.
   */
  backCurve?: number;
  /**
   * Brand recommendations or restrictions.
   */
  brand?: string;
  _brand?: Element;
  /**
   * Special color or pattern.
   */
  color?: string;
  _color?: Element;
  /**
   * Power adjustment for astigmatism measured in dioptres (0.25 units).
   */
  cylinder?: number;
  /**
   * Contact lens diameter measured in millimetres.
   */
  diameter?: number;
  /**
   * The recommended maximum wear period for the lens.
   */
  duration?: Quantity;
  /**
   * May also appear as OD (oculus dexter) for the right eye and OS (oculus siniter) for the left eye.
   */
  eye: string;
  _eye?: Element;
  /**
   * Notes for special requirements such as coatings and lens materials.
   */
  note?: Annotation[];
  /**
   * Contact lens power measured in dioptres (0.25 units).
   */
  power?: number;
  /**
   * Allows for adjustment on two axis.
   */
  prism?: VisionPrescriptionLensSpecificationPrism[];
  /**
   * Identifies the type of vision correction product which is required for the patient.
   */
  product: CodeableConcept;
  /**
   * The value is negative for near-sighted and positive for far sighted.
   * Often insurance will not cover a lens with power between +75 and -75.
   */
  sphere?: number;
}
/**
 * Code Values for the VisionPrescription.lensSpecification.eye field
 */
export enum VisionPrescriptionLensSpecificationEyeCodes {
  RIGHT = 'right',
  LEFT = 'left',
}
/**
 * An authorization for the provision of glasses and/or contact lenses to a patient.
 */
export interface VisionPrescription extends DomainResource {
  /** Resource Type Name (for serialization) */
  resourceType: 'VisionPrescription';
  /**
   * The date this resource was created.
   */
  created: string;
  _created?: Element;
  /**
   * Jurisdictions determine the valid lifetime of a prescription. Typically vision prescriptions are valid for two years from the date written.
   */
  dateWritten: string;
  _dateWritten?: Element;
  /**
   * A reference to a resource that identifies the particular occurrence of contact between patient and health care provider during which the prescription was issued.
   */
  encounter?: Reference;
  /**
   * A unique identifier assigned to this vision prescription.
   */
  identifier?: Identifier[];
  /**
   * Contain the details of  the individual lens specifications and serves as the authorization for the fullfillment by certified professionals.
   */
  lensSpecification: VisionPrescriptionLensSpecification[];
  /**
   * A resource reference to the person to whom the vision prescription applies.
   */
  patient: Reference;
  /**
   * The healthcare professional responsible for authorizing the prescription.
   */
  prescriber: Reference;
  /**
   * This element is labeled as a modifier because the status contains codes that mark the resource as not currently valid.
   */
  status: string;
  _status?: Element;
}
/**
 * Code Values for the VisionPrescription.status field
 */
export enum VisionPrescriptionStatusCodes {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  DRAFT = 'draft',
  ENTERED_IN_ERROR = 'entered-in-error',
}
const abstract_types_Any: Coding = {
  code: 'Any',
  display: 'Any',
  system: 'http://hl7.org/fhir/abstract-types',
};
const abstract_types_Type: Coding = {
  code: 'Type',
  display: 'Type',
  system: 'http://hl7.org/fhir/abstract-types',
};
/**
 * A list of the base types defined by this version of the FHIR specification - types that are defined, but for which only specializations actually are created.
 */
export const AbstractTypes = {
  /**
   * A place holder that means any kind of resource
   */
  Any: abstract_types_Any,
  /**
   * A place holder that means any kind of data type
   */
  Type: abstract_types_Type,
};
const account_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/account-status',
};
const account_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in error',
  system: 'http://hl7.org/fhir/account-status',
};
const account_status_Inactive: Coding = {
  code: 'inactive',
  display: 'Inactive',
  system: 'http://hl7.org/fhir/account-status',
};
const account_status_OnHold: Coding = {
  code: 'on-hold',
  display: 'On Hold',
  system: 'http://hl7.org/fhir/account-status',
};
const account_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/account-status',
};
/**
 * Indicates whether the account is available to be used.
 */
export const AccountStatus = {
  /**
   * This account is active and may be used.
   */
  Active: account_status_Active,
  /**
   * This instance should not have been part of this patient's medical record.
   */
  EnteredInError: account_status_EnteredInError,
  /**
   * This account is inactive and should not be used to track financial information.
   */
  Inactive: account_status_Inactive,
  /**
   * This account is on hold.
   */
  OnHold: account_status_OnHold,
  /**
   * The account status is unknown.
   */
  Unknown: account_status_Unknown,
};
const v3_ActCode_ActAccountCode: Coding = {
  code: '_ActAccountCode',
  display: 'ActAccountCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccountReceivable: Coding = {
  code: 'ACCTRECEIVABLE',
  display: 'account receivable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AmericanExpress: Coding = {
  code: 'AE',
  display: 'American Express',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Cash: Coding = {
  code: 'CASH',
  display: 'Cash',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CreditCard: Coding = {
  code: 'CC',
  display: 'credit card',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DinerQuoteSClub: Coding = {
  code: 'DN',
  display: "Diner's Club",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DiscoverCard: Coding = {
  code: 'DV',
  display: 'Discover Card',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MasterCard: Coding = {
  code: 'MC',
  display: 'Master Card',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PatientBillingAccount: Coding = {
  code: 'PBILLACCT',
  display: 'patient billing account',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Visa: Coding = {
  code: 'V',
  display: 'Visa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
/**
 * This examples value set defines the set of codes that can be used to represent the type of an account.
 */
export const AccountType = {
  /**
   * An account represents a grouping of financial transactions that are tracked and reported together with a single balance. 	 	Examples of account codes (types) are Patient billing accounts (collection of charges), Cost centers; Cash.
   */
  ActAccountCode: v3_ActCode_ActAccountCode,
  /**
   * An account for collecting charges, reversals, adjustments and payments, including deductibles, copayments, coinsurance (financial transactions) credited or debited to the account receivable account for a patient's encounter.
   */
  AccountReceivable: v3_ActCode_AccountReceivable,
  /**
   * American Express
   */
  AmericanExpress: v3_ActCode_AmericanExpress,
  /**
   * Cash
   */
  Cash: v3_ActCode_Cash,
  /**
   * Description: Types of advance payment to be made on a plastic card usually issued by a financial institution used of purchasing services and/or products.
   */
  CreditCard: v3_ActCode_CreditCard,
  /**
   * Diner's Club
   */
  DinerQuoteSClub: v3_ActCode_DinerQuoteSClub,
  /**
   * Discover Card
   */
  DiscoverCard: v3_ActCode_DiscoverCard,
  /**
   * Master Card
   */
  MasterCard: v3_ActCode_MasterCard,
  /**
   * An account representing charges and credits (financial transactions) for a patient's encounter.
   */
  PatientBillingAccount: v3_ActCode_PatientBillingAccount,
  /**
   * Visa
   */
  Visa: v3_ActCode_Visa,
};
const action_cardinality_behavior_Multiple: Coding = {
  code: 'multiple',
  display: 'Multiple',
  system: 'http://hl7.org/fhir/action-cardinality-behavior',
};
const action_cardinality_behavior_Single: Coding = {
  code: 'single',
  display: 'Single',
  system: 'http://hl7.org/fhir/action-cardinality-behavior',
};
/**
 * Defines behavior for an action or a group for how many times that item may be repeated.
 */
export const ActionCardinalityBehavior = {
  /**
   * The action may be selected multiple times.
   */
  Multiple: action_cardinality_behavior_Multiple,
  /**
   * The action may only be selected one time.
   */
  Single: action_cardinality_behavior_Single,
};
const action_condition_kind_Applicability: Coding = {
  code: 'applicability',
  display: 'Applicability',
  system: 'http://hl7.org/fhir/action-condition-kind',
};
const action_condition_kind_Start: Coding = {
  code: 'start',
  display: 'Start',
  system: 'http://hl7.org/fhir/action-condition-kind',
};
const action_condition_kind_Stop: Coding = {
  code: 'stop',
  display: 'Stop',
  system: 'http://hl7.org/fhir/action-condition-kind',
};
/**
 * Defines the kinds of conditions that can appear on actions.
 */
export const ActionConditionKind = {
  /**
   * The condition describes whether or not a given action is applicable.
   */
  Applicability: action_condition_kind_Applicability,
  /**
   * The condition is a starting condition for the action.
   */
  Start: action_condition_kind_Start,
  /**
   * The condition is a stop, or exit condition for the action.
   */
  Stop: action_condition_kind_Stop,
};
const action_grouping_behavior_LogicalGroup: Coding = {
  code: 'logical-group',
  display: 'Logical Group',
  system: 'http://hl7.org/fhir/action-grouping-behavior',
};
const action_grouping_behavior_SentenceGroup: Coding = {
  code: 'sentence-group',
  display: 'Sentence Group',
  system: 'http://hl7.org/fhir/action-grouping-behavior',
};
const action_grouping_behavior_VisualGroup: Coding = {
  code: 'visual-group',
  display: 'Visual Group',
  system: 'http://hl7.org/fhir/action-grouping-behavior',
};
/**
 * Defines organization behavior of a group.
 */
export const ActionGroupingBehavior = {
  /**
   * A group with this behavior logically groups its sub-elements, and may be shown as a visual group to the end user, but it is not required to do so.
   */
  LogicalGroup: action_grouping_behavior_LogicalGroup,
  /**
   * A group of related alternative actions is a sentence group if the target referenced by the action is the same in all the actions and each action simply constitutes a different variation on how to specify the details for the target. For example, two actions that could be in a SentenceGroup are "aspirin, 500 mg, 2 times per day" and "aspirin, 300 mg, 3 times per day". In both cases, aspirin is the target referenced by the action, and the two actions represent different options for how aspirin might be ordered for the patient. Note that a SentenceGroup would almost always have an associated selection behavior of "AtMostOne", unless it's a required action, in which case, it would be "ExactlyOne".
   */
  SentenceGroup: action_grouping_behavior_SentenceGroup,
  /**
   * Any group marked with this behavior should be displayed as a visual group to the end user.
   */
  VisualGroup: action_grouping_behavior_VisualGroup,
};
const action_participant_type_Device: Coding = {
  code: 'device',
  display: 'Device',
  system: 'http://hl7.org/fhir/action-participant-type',
};
const action_participant_type_Patient: Coding = {
  code: 'patient',
  display: 'Patient',
  system: 'http://hl7.org/fhir/action-participant-type',
};
const action_participant_type_Practitioner: Coding = {
  code: 'practitioner',
  display: 'Practitioner',
  system: 'http://hl7.org/fhir/action-participant-type',
};
const action_participant_type_RelatedPerson: Coding = {
  code: 'related-person',
  display: 'Related Person',
  system: 'http://hl7.org/fhir/action-participant-type',
};
/**
 * The type of participant for the action.
 */
export const ActionParticipantType = {
  /**
   * The participant is a system or device used in the care of the patient.
   */
  Device: action_participant_type_Device,
  /**
   * The participant is the patient under evaluation.
   */
  Patient: action_participant_type_Patient,
  /**
   * The participant is a practitioner involved in the patient's care.
   */
  Practitioner: action_participant_type_Practitioner,
  /**
   * The participant is a person related to the patient.
   */
  RelatedPerson: action_participant_type_RelatedPerson,
};
const action_precheck_behavior_No: Coding = {
  code: 'no',
  display: 'No',
  system: 'http://hl7.org/fhir/action-precheck-behavior',
};
const action_precheck_behavior_Yes: Coding = {
  code: 'yes',
  display: 'Yes',
  system: 'http://hl7.org/fhir/action-precheck-behavior',
};
/**
 * Defines selection frequency behavior for an action or group.
 */
export const ActionPrecheckBehavior = {
  /**
   * An action with this behavior is one of the less frequent actions included by the end user, for the particular context in which the action occurs. The system displaying the actions to the end user would typically not "pre-check" such an action.
   */
  No: action_precheck_behavior_No,
  /**
   * An action with this behavior is one of the most frequent action that is, or should be, included by an end user, for the particular context in which the action occurs. The system displaying the action to the end user should consider "pre-checking" such an action as a convenience for the user.
   */
  Yes: action_precheck_behavior_Yes,
};
const action_relationship_type_After: Coding = {
  code: 'after',
  display: 'After',
  system: 'http://hl7.org/fhir/action-relationship-type',
};
const action_relationship_type_AfterEnd: Coding = {
  code: 'after-end',
  display: 'After End',
  system: 'http://hl7.org/fhir/action-relationship-type',
};
const action_relationship_type_AfterStart: Coding = {
  code: 'after-start',
  display: 'After Start',
  system: 'http://hl7.org/fhir/action-relationship-type',
};
const action_relationship_type_Before: Coding = {
  code: 'before',
  display: 'Before',
  system: 'http://hl7.org/fhir/action-relationship-type',
};
const action_relationship_type_BeforeEnd: Coding = {
  code: 'before-end',
  display: 'Before End',
  system: 'http://hl7.org/fhir/action-relationship-type',
};
const action_relationship_type_BeforeStart: Coding = {
  code: 'before-start',
  display: 'Before Start',
  system: 'http://hl7.org/fhir/action-relationship-type',
};
const action_relationship_type_Concurrent: Coding = {
  code: 'concurrent',
  display: 'Concurrent',
  system: 'http://hl7.org/fhir/action-relationship-type',
};
const action_relationship_type_ConcurrentWithEnd: Coding = {
  code: 'concurrent-with-end',
  display: 'Concurrent With End',
  system: 'http://hl7.org/fhir/action-relationship-type',
};
const action_relationship_type_ConcurrentWithStart: Coding = {
  code: 'concurrent-with-start',
  display: 'Concurrent With Start',
  system: 'http://hl7.org/fhir/action-relationship-type',
};
/**
 * Defines the types of relationships between actions.
 */
export const ActionRelationshipType = {
  /**
   * The action must be performed after the related action.
   */
  After: action_relationship_type_After,
  /**
   * The action must be performed after the end of the related action.
   */
  AfterEnd: action_relationship_type_AfterEnd,
  /**
   * The action must be performed after the start of the related action.
   */
  AfterStart: action_relationship_type_AfterStart,
  /**
   * The action must be performed before the related action.
   */
  Before: action_relationship_type_Before,
  /**
   * The action must be performed before the end of the related action.
   */
  BeforeEnd: action_relationship_type_BeforeEnd,
  /**
   * The action must be performed before the start of the related action.
   */
  BeforeStart: action_relationship_type_BeforeStart,
  /**
   * The action must be performed concurrent with the related action.
   */
  Concurrent: action_relationship_type_Concurrent,
  /**
   * The action must be performed concurrent with the end of the related action.
   */
  ConcurrentWithEnd: action_relationship_type_ConcurrentWithEnd,
  /**
   * The action must be performed concurrent with the start of the related action.
   */
  ConcurrentWithStart: action_relationship_type_ConcurrentWithStart,
};
const action_required_behavior_Could: Coding = {
  code: 'could',
  display: 'Could',
  system: 'http://hl7.org/fhir/action-required-behavior',
};
const action_required_behavior_Must: Coding = {
  code: 'must',
  display: 'Must',
  system: 'http://hl7.org/fhir/action-required-behavior',
};
const action_required_behavior_MustUnlessDocumented: Coding = {
  code: 'must-unless-documented',
  display: 'Must Unless Documented',
  system: 'http://hl7.org/fhir/action-required-behavior',
};
/**
 * Defines expectations around whether an action or action group is required.
 */
export const ActionRequiredBehavior = {
  /**
   * An action with this behavior may be included in the set of actions processed by the end user.
   */
  Could: action_required_behavior_Could,
  /**
   * An action with this behavior must be included in the actions processed by the end user; the end user SHALL NOT choose not to include this action.
   */
  Must: action_required_behavior_Must,
  /**
   * An action with this behavior must be included in the set of actions processed by the end user, unless the end user provides documentation as to why the action was not included.
   */
  MustUnlessDocumented: action_required_behavior_MustUnlessDocumented,
};
const action_selection_behavior_All: Coding = {
  code: 'all',
  display: 'All',
  system: 'http://hl7.org/fhir/action-selection-behavior',
};
const action_selection_behavior_AllOrNone: Coding = {
  code: 'all-or-none',
  display: 'All Or None',
  system: 'http://hl7.org/fhir/action-selection-behavior',
};
const action_selection_behavior_Any: Coding = {
  code: 'any',
  display: 'Any',
  system: 'http://hl7.org/fhir/action-selection-behavior',
};
const action_selection_behavior_AtMostOne: Coding = {
  code: 'at-most-one',
  display: 'At Most One',
  system: 'http://hl7.org/fhir/action-selection-behavior',
};
const action_selection_behavior_ExactlyOne: Coding = {
  code: 'exactly-one',
  display: 'Exactly One',
  system: 'http://hl7.org/fhir/action-selection-behavior',
};
const action_selection_behavior_OneOrMore: Coding = {
  code: 'one-or-more',
  display: 'One Or More',
  system: 'http://hl7.org/fhir/action-selection-behavior',
};
/**
 * Defines selection behavior of a group.
 */
export const ActionSelectionBehavior = {
  /**
   * All the actions in the group must be selected as a single unit.
   */
  All: action_selection_behavior_All,
  /**
   * All the actions in the group are meant to be chosen as a single unit: either all must be selected by the end user, or none may be selected.
   */
  AllOrNone: action_selection_behavior_AllOrNone,
  /**
   * Any number of the actions in the group may be chosen, from zero to all.
   */
  Any: action_selection_behavior_Any,
  /**
   * The end user may choose zero or at most one of the actions in the group.
   */
  AtMostOne: action_selection_behavior_AtMostOne,
  /**
   * The end user must choose one and only one of the selectable actions in the group. The user SHALL NOT choose none of the actions in the group.
   */
  ExactlyOne: action_selection_behavior_ExactlyOne,
  /**
   * The end user must choose a minimum of one, and as many additional as desired.
   */
  OneOrMore: action_selection_behavior_OneOrMore,
};
const action_type_Create: Coding = {
  code: 'create',
  display: 'Create',
  system: 'http://terminology.hl7.org/CodeSystem/action-type',
};
const action_type_FireEvent: Coding = {
  code: 'fire-event',
  display: 'Fire Event',
  system: 'http://terminology.hl7.org/CodeSystem/action-type',
};
const action_type_Remove: Coding = {
  code: 'remove',
  display: 'Remove',
  system: 'http://terminology.hl7.org/CodeSystem/action-type',
};
const action_type_Update: Coding = {
  code: 'update',
  display: 'Update',
  system: 'http://terminology.hl7.org/CodeSystem/action-type',
};
/**
 * The type of action to be performed.
 */
export const ActionType = {
  /**
   * The action is to create a new resource.
   */
  Create: action_type_Create,
  /**
   * The action is to fire a specific event.
   */
  FireEvent: action_type_FireEvent,
  /**
   * The action is to remove an existing resource.
   */
  Remove: action_type_Remove,
  /**
   * The action is to update an existing resource.
   */
  Update: action_type_Update,
};
const activity_definition_category_Assessment: Coding = {
  code: 'assessment',
  display: 'Assessment',
  system: 'http://terminology.hl7.org/CodeSystem/activity-definition-category',
};
const activity_definition_category_Education: Coding = {
  code: 'education',
  display: 'Education',
  system: 'http://terminology.hl7.org/CodeSystem/activity-definition-category',
};
const activity_definition_category_Treatment: Coding = {
  code: 'treatment',
  display: 'Treatment',
  system: 'http://terminology.hl7.org/CodeSystem/activity-definition-category',
};
/**
 * High-level categorization of the type of activity.
 */
export const ActivityDefinitionCategory = {
  /**
   * The activity is intended to perform or is related to assessment of the patient.
   */
  Assessment: activity_definition_category_Assessment,
  /**
   * The activity is intended to provide or is related to education of the patient.
   */
  Education: activity_definition_category_Education,
  /**
   * The activity is intended to provide or is related to treatment of the patient.
   */
  Treatment: activity_definition_category_Treatment,
};
const additionalmaterials_Document: Coding = {
  code: 'document',
  display: 'Document',
  system: 'http://hl7.org/fhir/additionalmaterials',
};
const additionalmaterials_Email: Coding = {
  code: 'email',
  display: 'Email',
  system: 'http://hl7.org/fhir/additionalmaterials',
};
const additionalmaterials_Image: Coding = {
  code: 'image',
  display: 'Image',
  system: 'http://hl7.org/fhir/additionalmaterials',
};
const additionalmaterials_Model: Coding = {
  code: 'model',
  display: 'Model',
  system: 'http://hl7.org/fhir/additionalmaterials',
};
const additionalmaterials_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://hl7.org/fhir/additionalmaterials',
};
const additionalmaterials_XRay: Coding = {
  code: 'xray',
  display: 'XRay',
  system: 'http://hl7.org/fhir/additionalmaterials',
};
/**
 * This value set includes sample additional material type codes.
 */
export const Additionalmaterials = {
  /**
   * Document
   */
  Document: additionalmaterials_Document,
  /**
   * Email
   */
  Email: additionalmaterials_Email,
  /**
   * Image
   */
  Image: additionalmaterials_Image,
  /**
   * Model
   */
  Model: additionalmaterials_Model,
  /**
   * Other
   */
  Other: additionalmaterials_Other,
  /**
   * XRay
   */
  XRay: additionalmaterials_XRay,
};
const address_type_PostalAndPhysical: Coding = {
  code: 'both',
  display: 'Postal & Physical',
  system: 'http://hl7.org/fhir/address-type',
};
const address_type_Physical: Coding = {
  code: 'physical',
  display: 'Physical',
  system: 'http://hl7.org/fhir/address-type',
};
const address_type_Postal: Coding = {
  code: 'postal',
  display: 'Postal',
  system: 'http://hl7.org/fhir/address-type',
};
/**
 * The type of an address (physical / postal).
 */
export const AddressType = {
  /**
   * An address that is both physical and postal.
   */
  PostalAndPhysical: address_type_PostalAndPhysical,
  /**
   * A physical address that can be visited.
   */
  Physical: address_type_Physical,
  /**
   * Mailing addresses - PO Boxes and care-of addresses.
   */
  Postal: address_type_Postal,
};
const address_use_Billing: Coding = {
  code: 'billing',
  display: 'Billing',
  system: 'http://hl7.org/fhir/address-use',
};
const address_use_Home: Coding = {
  code: 'home',
  display: 'Home',
  system: 'http://hl7.org/fhir/address-use',
};
const address_use_OldIncorrect: Coding = {
  code: 'old',
  display: 'Old / Incorrect',
  system: 'http://hl7.org/fhir/address-use',
};
const address_use_Temporary: Coding = {
  code: 'temp',
  display: 'Temporary',
  system: 'http://hl7.org/fhir/address-use',
};
const address_use_Work: Coding = {
  code: 'work',
  display: 'Work',
  system: 'http://hl7.org/fhir/address-use',
};
/**
 * The use of an address.
 */
export const AddressUse = {
  /**
   * An address to be used to send bills, invoices, receipts etc.
   */
  Billing: address_use_Billing,
  /**
   * A communication address at a home.
   */
  Home: address_use_Home,
  /**
   * This address is no longer in use (or was never correct but retained for records).
   */
  OldIncorrect: address_use_OldIncorrect,
  /**
   * A temporary address. The period can provide more detailed information.
   */
  Temporary: address_use_Temporary,
  /**
   * An office address. First choice for business related contacts during business hours.
   */
  Work: address_use_Work,
};
const adjudication_BenefitAmount: Coding = {
  code: 'benefit',
  display: 'Benefit Amount',
  system: 'http://terminology.hl7.org/CodeSystem/adjudication',
};
const adjudication_CoPay: Coding = {
  code: 'copay',
  display: 'CoPay',
  system: 'http://terminology.hl7.org/CodeSystem/adjudication',
};
const adjudication_Deductible: Coding = {
  code: 'deductible',
  display: 'Deductible',
  system: 'http://terminology.hl7.org/CodeSystem/adjudication',
};
const adjudication_EligibleAmount: Coding = {
  code: 'eligible',
  display: 'Eligible Amount',
  system: 'http://terminology.hl7.org/CodeSystem/adjudication',
};
const adjudication_EligiblePercent: Coding = {
  code: 'eligpercent',
  display: 'Eligible %',
  system: 'http://terminology.hl7.org/CodeSystem/adjudication',
};
const adjudication_SubmittedAmount: Coding = {
  code: 'submitted',
  display: 'Submitted Amount',
  system: 'http://terminology.hl7.org/CodeSystem/adjudication',
};
const adjudication_Tax: Coding = {
  code: 'tax',
  display: 'Tax',
  system: 'http://terminology.hl7.org/CodeSystem/adjudication',
};
const adjudication_UnallocatedDeductible: Coding = {
  code: 'unallocdeduct',
  display: 'Unallocated Deductible',
  system: 'http://terminology.hl7.org/CodeSystem/adjudication',
};
/**
 * This value set includes a smattering of Adjudication Value codes which includes codes to indicate the amounts eligible under the plan, the amount of benefit, copays etc.
 */
export const Adjudication = {
  /**
   * Amount payable under the coverage
   */
  BenefitAmount: adjudication_BenefitAmount,
  /**
   * Patient Co-Payment
   */
  CoPay: adjudication_CoPay,
  /**
   * Amount deducted from the eligible amount prior to adjudication.
   */
  Deductible: adjudication_Deductible,
  /**
   * Amount of the change which is considered for adjudication.
   */
  EligibleAmount: adjudication_EligibleAmount,
  /**
   * Eligible Percentage.
   */
  EligiblePercent: adjudication_EligiblePercent,
  /**
   * The total submitted amount for the claim or group or line item.
   */
  SubmittedAmount: adjudication_SubmittedAmount,
  /**
   * The amount of tax.
   */
  Tax: adjudication_Tax,
  /**
   * The amount of deductible which could not allocated to other line items.
   */
  UnallocatedDeductible: adjudication_UnallocatedDeductible,
};
const adjudication_error_MissingIdentifier: Coding = {
  code: 'a001',
  display: 'Missing Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/adjudication-error',
};
const adjudication_error_MissingCreationDate: Coding = {
  code: 'a002',
  display: 'Missing Creation Date',
  system: 'http://terminology.hl7.org/CodeSystem/adjudication-error',
};
/**
 * This value set includes a smattering of adjudication codes.
 */
export const AdjudicationError = {
  /**
   * Missing Identifier
   */
  MissingIdentifier: adjudication_error_MissingIdentifier,
  /**
   * Missing Creation Date
   */
  MissingCreationDate: adjudication_error_MissingCreationDate,
};
const adjudication_reason_NotCovered: Coding = {
  code: 'ar001',
  display: 'Not covered',
  system: 'http://terminology.hl7.org/CodeSystem/adjudication-reason',
};
const adjudication_reason_PlanLimitReached: Coding = {
  code: 'ar002',
  display: 'Plan Limit Reached',
  system: 'http://terminology.hl7.org/CodeSystem/adjudication-reason',
};
/**
 * This value set includes smattering of Adjudication Reason codes.
 */
export const AdjudicationReason = {
  /**
   * Not covered
   */
  NotCovered: adjudication_reason_NotCovered,
  /**
   * Plan Limit Reached
   */
  PlanLimitReached: adjudication_reason_PlanLimitReached,
};
const administrative_gender_Female: Coding = {
  code: 'female',
  display: 'Female',
  system: 'http://hl7.org/fhir/administrative-gender',
};
const administrative_gender_Male: Coding = {
  code: 'male',
  display: 'Male',
  system: 'http://hl7.org/fhir/administrative-gender',
};
const administrative_gender_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://hl7.org/fhir/administrative-gender',
};
const administrative_gender_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/administrative-gender',
};
/**
 * The gender of a person used for administrative purposes.
 */
export const AdministrativeGender = {
  /**
   * Female.
   */
  Female: administrative_gender_Female,
  /**
   * Male.
   */
  Male: administrative_gender_Male,
  /**
   * Other.
   */
  Other: administrative_gender_Other,
  /**
   * Unknown.
   */
  Unknown: administrative_gender_Unknown,
};
const adverse_event_actuality_AdverseEvent: Coding = {
  code: 'actual',
  display: 'Adverse Event',
  system: 'http://hl7.org/fhir/adverse-event-actuality',
};
const adverse_event_actuality_PotentialAdverseEvent: Coding = {
  code: 'potential',
  display: 'Potential Adverse Event',
  system: 'http://hl7.org/fhir/adverse-event-actuality',
};
/**
 * Overall nature of the adverse event, e.g. real or potential.
 */
export const AdverseEventActuality = {
  /**
   * The adverse event actually happened regardless of whether anyone was affected or harmed.
   */
  AdverseEvent: adverse_event_actuality_AdverseEvent,
  /**
   * A potential adverse event.
   */
  PotentialAdverseEvent: adverse_event_actuality_PotentialAdverseEvent,
};
const adverse_event_category_ExpiredDrug: Coding = {
  code: 'expired-drug',
  display: 'Expired Drug',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
const adverse_event_category_IncorrectPrescribingInformation: Coding = {
  code: 'incorrect-prescribing-information',
  display: 'Incorrect Prescribing Information',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
const adverse_event_category_MedicalDeviceUseError: Coding = {
  code: 'medical-device-use-error',
  display: 'Medical Device Use Error',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
const adverse_event_category_ProblemWithDifferentManufacturerOfSameMedicine: Coding = {
  code: 'problem-different-manufacturer',
  display: 'Problem with Different Manufacturer of Same Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
const adverse_event_category_ProductProblem: Coding = {
  code: 'product-problem',
  display: 'Product Problem',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
const adverse_event_category_ProductQuality: Coding = {
  code: 'product-quality',
  display: 'Product Quality',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
const adverse_event_category_ProductUseError: Coding = {
  code: 'product-use-error',
  display: 'Product Use Error',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
const adverse_event_category_UnsafePhysicalEnvironment: Coding = {
  code: 'unsafe-physical-environment',
  display: 'Unsafe Physical Environment',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
const adverse_event_category_WrongDose: Coding = {
  code: 'wrong-dose',
  display: 'Wrong Dose',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
const adverse_event_category_WrongDuration: Coding = {
  code: 'wrong-duration',
  display: 'Wrong Duration',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
const adverse_event_category_WrongRate: Coding = {
  code: 'wrong-rate',
  display: 'Wrong Rate',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
const adverse_event_category_WrongRouteOfAdministration: Coding = {
  code: 'wrong-route-of-administration',
  display: 'Wrong Route of Administration',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
const adverse_event_category_WrongTechnique: Coding = {
  code: 'wrong-technique',
  display: 'Wrong Technique',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
const adverse_event_category_WrongTime: Coding = {
  code: 'wrong-time',
  display: 'Wrong Time',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-category',
};
/**
 * Overall categorization of the event, e.g. product-related or situational.
 */
export const AdverseEventCategory = {
  /**
   * The adverse event pertains to an expired drug.
   */
  ExpiredDrug: adverse_event_category_ExpiredDrug,
  /**
   * The adverse event pertains to incorrect perscribing information.
   */
  IncorrectPrescribingInformation: adverse_event_category_IncorrectPrescribingInformation,
  /**
   * The adverse event pertains to a medical device use error.
   */
  MedicalDeviceUseError: adverse_event_category_MedicalDeviceUseError,
  /**
   * The adverse event pertains to a problem with a different manufacturer of the same medication.
   */
  ProblemWithDifferentManufacturerOfSameMedicine: adverse_event_category_ProblemWithDifferentManufacturerOfSameMedicine,
  /**
   * The adverse event pertains to a product problem.
   */
  ProductProblem: adverse_event_category_ProductProblem,
  /**
   * The adverse event pertains to product quality.
   */
  ProductQuality: adverse_event_category_ProductQuality,
  /**
   * The adverse event pertains to a product use error.
   */
  ProductUseError: adverse_event_category_ProductUseError,
  /**
   * The adverse event pertains to an unsafe physical environment.
   */
  UnsafePhysicalEnvironment: adverse_event_category_UnsafePhysicalEnvironment,
  /**
   * The adverse event pertains to a wrong dose.
   */
  WrongDose: adverse_event_category_WrongDose,
  /**
   * The adverse event pertains to a wrong duration.
   */
  WrongDuration: adverse_event_category_WrongDuration,
  /**
   * The adverse event pertains to a wrong rate.
   */
  WrongRate: adverse_event_category_WrongRate,
  /**
   * The adverse event pertains to a wrong route of administration.
   */
  WrongRouteOfAdministration: adverse_event_category_WrongRouteOfAdministration,
  /**
   * The adverse event pertains to a wrong technique.
   */
  WrongTechnique: adverse_event_category_WrongTechnique,
  /**
   * The adverse event pertains to a wrong time.
   */
  WrongTime: adverse_event_category_WrongTime,
};
const adverse_event_causality_assess_Certain: Coding = {
  code: 'Certain',
  display: 'Certain',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-causality-assess',
};
const adverse_event_causality_assess_ConditionalClassified: Coding = {
  code: 'Conditional-Classified',
  display: 'Conditional/Classified',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-causality-assess',
};
const adverse_event_causality_assess_Possible: Coding = {
  code: 'Possible',
  display: 'Possible',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-causality-assess',
};
const adverse_event_causality_assess_ProbablyLikely: Coding = {
  code: 'Probably-Likely',
  display: 'Probably/Likely',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-causality-assess',
};
const adverse_event_causality_assess_UnassessableUnclassifiable: Coding = {
  code: 'Unassessable-Unclassifiable',
  display: 'Unassessable/Unclassifiable',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-causality-assess',
};
const adverse_event_causality_assess_Unlikely: Coding = {
  code: 'Unlikely',
  display: 'Unlikely',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-causality-assess',
};
/**
 * Codes for the assessment of whether the entity caused the event.
 */
export const AdverseEventCausalityAssess = {
  /**
   * i) Event or laboratory test abnormality, with plausible time relationship to drug intake; ii) Cannot be explained by disease or other drugs; iii) Response to withdrawal plausible (pharmacologically, pathologically); iv) Event definitive pharmacologically or phenomenologically (i.e. an objective and specific medical disorder or a recognized pharmacological phenomenon); or v) Re-challenge satisfactory, if necessary.
   */
  Certain: adverse_event_causality_assess_Certain,
  /**
   * i) Event or laboratory test abnormality; ii) More data for proper assessment needed; or iii) Additional data under examination.
   */
  ConditionalClassified: adverse_event_causality_assess_ConditionalClassified,
  /**
   * i) Event or laboratory test abnormality, with reasonable time relationship to drug intake; ii) Could also be explained by disease or other drugs; or iii) Information on drug withdrawal may be lacking or unclear.
   */
  Possible: adverse_event_causality_assess_Possible,
  /**
   * i) Event or laboratory test abnormality, with reasonable time relationship to drug intake; ii) Unlikely to be attributed to disease or other drugs; iii) Response to withdrawal clinically reasonable; or iv) Re-challenge not required.
   */
  ProbablyLikely: adverse_event_causality_assess_ProbablyLikely,
  /**
   * i) Report suggesting an adverse reaction; ii) Cannot be judged because information is insufficient or contradictory; or iii) Data cannot be supplemented or verified.
   */
  UnassessableUnclassifiable: adverse_event_causality_assess_UnassessableUnclassifiable,
  /**
   * i) Event or laboratory test abnormality, with a time to drug intake that makes a relationship improbable (but not impossible); or ii) Disease or other drugs provide plausible explanations.
   */
  Unlikely: adverse_event_causality_assess_Unlikely,
};
const adverse_event_causality_method_Bayesian: Coding = {
  code: 'Bayesian',
  display: 'Bayesian',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-causality-method',
};
const adverse_event_causality_method_Checklist: Coding = {
  code: 'Checklist',
  display: 'Checklist',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-causality-method',
};
const adverse_event_causality_method_ProbabilityScale: Coding = {
  code: 'ProbabilityScale',
  display: 'Probability Scale',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-causality-method',
};
/**
 * TODO.
 */
export const AdverseEventCausalityMethod = {
  Bayesian: adverse_event_causality_method_Bayesian,
  Checklist: adverse_event_causality_method_Checklist,
  ProbabilityScale: adverse_event_causality_method_ProbabilityScale,
};
const adverse_event_outcome_Fatal: Coding = {
  code: 'fatal',
  display: 'Fatal',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-outcome',
};
const adverse_event_outcome_Ongoing: Coding = {
  code: 'ongoing',
  display: 'Ongoing',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-outcome',
};
const adverse_event_outcome_Recovering: Coding = {
  code: 'recovering',
  display: 'Recovering',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-outcome',
};
const adverse_event_outcome_Resolved: Coding = {
  code: 'resolved',
  display: 'Resolved',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-outcome',
};
const adverse_event_outcome_ResolvedWithSequelae: Coding = {
  code: 'resolvedWithSequelae',
  display: 'Resolved with Sequelae',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-outcome',
};
const adverse_event_outcome_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-outcome',
};
/**
 * TODO (and should this be required?).
 */
export const AdverseEventOutcome = {
  Fatal: adverse_event_outcome_Fatal,
  Ongoing: adverse_event_outcome_Ongoing,
  Recovering: adverse_event_outcome_Recovering,
  Resolved: adverse_event_outcome_Resolved,
  ResolvedWithSequelae: adverse_event_outcome_ResolvedWithSequelae,
  Unknown: adverse_event_outcome_Unknown,
};
const adverse_event_seriousness_NonSerious: Coding = {
  code: 'Non-serious',
  display: 'Non-serious',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-seriousness',
};
const adverse_event_seriousness_Serious: Coding = {
  code: 'Serious',
  display: 'Serious',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-seriousness',
};
const adverse_event_seriousness_IsACongenitalAnomalyBirthDefect: Coding = {
  code: 'SeriousIsBirthDefect',
  display: 'Is a congenital anomaly/birth defect',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-seriousness',
};
const adverse_event_seriousness_IsLifeThreatening: Coding = {
  code: 'SeriousIsLifeThreatening',
  display: 'Is Life-threatening',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-seriousness',
};
const adverse_event_seriousness_RequiresInterventionToPreventPermanentImpairment: Coding = {
  code: 'SeriousRequiresPreventImpairment',
  display: 'Requires intervention to prevent permanent impairment',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-seriousness',
};
const adverse_event_seriousness_ResultsInDeath: Coding = {
  code: 'SeriousResultsInDeath',
  display: 'Results in death',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-seriousness',
};
const adverse_event_seriousness_ResultsInPersistentOrSignificantDisabilityIncapacity: Coding = {
  code: 'SeriousResultsInDisability',
  display: 'Results in persistent or significant disability/incapacity',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-seriousness',
};
const adverse_event_seriousness_RequiresOrProlongsInpatientHospitalization: Coding = {
  code: 'SeriousResultsInHospitalization',
  display: 'Requires or prolongs inpatient hospitalization',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-seriousness',
};
/**
 * Overall seriousness of this event for the patient.
 */
export const AdverseEventSeriousness = {
  /**
   * Non-serious.
   */
  NonSerious: adverse_event_seriousness_NonSerious,
  /**
   * Serious.
   */
  Serious: adverse_event_seriousness_Serious,
  /**
   * Is a congenital anomaly/birth defect.
   */
  IsACongenitalAnomalyBirthDefect: adverse_event_seriousness_IsACongenitalAnomalyBirthDefect,
  /**
   * Is Life-threatening.
   */
  IsLifeThreatening: adverse_event_seriousness_IsLifeThreatening,
  /**
   * Requires intervention to prevent permanent impairment or damage (i.e., an important medical event that requires medical judgement).
   */
  RequiresInterventionToPreventPermanentImpairment: adverse_event_seriousness_RequiresInterventionToPreventPermanentImpairment,
  /**
   * Results in death.
   */
  ResultsInDeath: adverse_event_seriousness_ResultsInDeath,
  /**
   * Results in persistent or significant disability/incapacity.
   */
  ResultsInPersistentOrSignificantDisabilityIncapacity: adverse_event_seriousness_ResultsInPersistentOrSignificantDisabilityIncapacity,
  /**
   * Requires inpatient hospitalization or causes prolongation of existing hospitalization.
   */
  RequiresOrProlongsInpatientHospitalization: adverse_event_seriousness_RequiresOrProlongsInpatientHospitalization,
};
const adverse_event_severity_Mild: Coding = {
  code: 'mild',
  display: 'Mild',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-severity',
};
const adverse_event_severity_Moderate: Coding = {
  code: 'moderate',
  display: 'Moderate',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-severity',
};
const adverse_event_severity_Severe: Coding = {
  code: 'severe',
  display: 'Severe',
  system: 'http://terminology.hl7.org/CodeSystem/adverse-event-severity',
};
/**
 * The severity of the adverse event itself, in direct relation to the subject.
 */
export const AdverseEventSeverity = {
  Mild: adverse_event_severity_Mild,
  Moderate: adverse_event_severity_Moderate,
  Severe: adverse_event_severity_Severe,
};
const AgeUnits_Years: Coding = {
  code: 'a',
  display: 'years',
  system: 'http://unitsofmeasure.org',
};
const AgeUnits_Days: Coding = {
  code: 'd',
  display: 'days',
  system: 'http://unitsofmeasure.org',
};
const AgeUnits_Hours: Coding = {
  code: 'h',
  display: 'hours',
  system: 'http://unitsofmeasure.org',
};
const AgeUnits_Minutes: Coding = {
  code: 'min',
  display: 'minutes',
  system: 'http://unitsofmeasure.org',
};
const AgeUnits_Months: Coding = {
  code: 'mo',
  display: 'months',
  system: 'http://unitsofmeasure.org',
};
const AgeUnits_Weeks: Coding = {
  code: 'wk',
  display: 'weeks',
  system: 'http://unitsofmeasure.org',
};
/**
 * Unified Code for Units of Measure (UCUM). This value set includes all UCUM codes
 */
export const AgeUnits = {
  Years: AgeUnits_Years,
  Days: AgeUnits_Days,
  Hours: AgeUnits_Hours,
  Minutes: AgeUnits_Minutes,
  Months: AgeUnits_Months,
  Weeks: AgeUnits_Weeks,
};
const allerg_intol_substance_exp_risk_KnownReactionRisk: Coding = {
  code: 'known-reaction-risk',
  display: 'Known Reaction Risk',
  system: 'http://terminology.hl7.org/CodeSystem/allerg-intol-substance-exp-risk',
};
const allerg_intol_substance_exp_risk_NoKnownReactionRisk: Coding = {
  code: 'no-known-reaction-risk',
  display: 'No Known Reaction Risk',
  system: 'http://terminology.hl7.org/CodeSystem/allerg-intol-substance-exp-risk',
};
/**
 * The risk of an adverse reaction (allergy or intolerance) for this patient upon exposure to the substance (including pharmaceutical products).
 */
export const AllergIntolSubstanceExpRisk = {
  /**
   * Known risk of allergy or intolerance reaction upon exposure to the specified substance.
   */
  KnownReactionRisk: allerg_intol_substance_exp_risk_KnownReactionRisk,
  /**
   * No known risk of allergy or intolerance reaction upon exposure to the specified substance.
   */
  NoKnownReactionRisk: allerg_intol_substance_exp_risk_NoKnownReactionRisk,
};
const allergy_intolerance_category_Biologic: Coding = {
  code: 'biologic',
  display: 'Biologic',
  system: 'http://hl7.org/fhir/allergy-intolerance-category',
};
const allergy_intolerance_category_Environment: Coding = {
  code: 'environment',
  display: 'Environment',
  system: 'http://hl7.org/fhir/allergy-intolerance-category',
};
const allergy_intolerance_category_Food: Coding = {
  code: 'food',
  display: 'Food',
  system: 'http://hl7.org/fhir/allergy-intolerance-category',
};
const allergy_intolerance_category_Medication: Coding = {
  code: 'medication',
  display: 'Medication',
  system: 'http://hl7.org/fhir/allergy-intolerance-category',
};
/**
 * Category of an identified substance associated with allergies or intolerances.
 */
export const AllergyIntoleranceCategory = {
  /**
   * A preparation that is synthesized from living organisms or their products, especially a human or animal protein, such as a hormone or antitoxin, that is used as a diagnostic, preventive, or therapeutic agent. Examples of biologic medications include: vaccines; allergenic extracts, which are used for both diagnosis and treatment (for example, allergy shots); gene therapies; cellular therapies.  There are other biologic products, such as tissues, which are not typically associated with allergies.
   */
  Biologic: allergy_intolerance_category_Biologic,
  /**
   * Any substances that are encountered in the environment, including any substance not already classified as food, medication, or biologic.
   */
  Environment: allergy_intolerance_category_Environment,
  /**
   * Any substance consumed to provide nutritional support for the body.
   */
  Food: allergy_intolerance_category_Food,
  /**
   * Substances administered to achieve a physiological effect.
   */
  Medication: allergy_intolerance_category_Medication,
};
const allergyintolerance_clinical_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
};
const allergyintolerance_clinical_Inactive: Coding = {
  code: 'inactive',
  display: 'Inactive',
  system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
};
const allergyintolerance_clinical_Resolved: Coding = {
  code: 'resolved',
  display: 'Resolved',
  system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
};
/**
 * Preferred value set for AllergyIntolerance Clinical Status.
 */
export const AllergyintoleranceClinical = {
  /**
   * The subject is currently experiencing, or is at risk of, a reaction to the identified substance.
   */
  Active: allergyintolerance_clinical_Active,
  /**
   * The subject is no longer at risk of a reaction to the identified substance.
   */
  Inactive: allergyintolerance_clinical_Inactive,
  /**
   * A reaction to the identified substance has been clinically reassessed by testing or re-exposure and is considered no longer to be present. Re-exposure could be accidental, unplanned, or outside of any clinical setting.
   */
  Resolved: allergyintolerance_clinical_Resolved,
};
const allergy_intolerance_criticality_HighRisk: Coding = {
  code: 'high',
  display: 'High Risk',
  system: 'http://hl7.org/fhir/allergy-intolerance-criticality',
};
const allergy_intolerance_criticality_LowRisk: Coding = {
  code: 'low',
  display: 'Low Risk',
  system: 'http://hl7.org/fhir/allergy-intolerance-criticality',
};
const allergy_intolerance_criticality_UnableToAssessRisk: Coding = {
  code: 'unable-to-assess',
  display: 'Unable to Assess Risk',
  system: 'http://hl7.org/fhir/allergy-intolerance-criticality',
};
/**
 * Estimate of the potential clinical harm, or seriousness, of a reaction to an identified substance.
 */
export const AllergyIntoleranceCriticality = {
  /**
   * Worst case result of a future exposure is assessed to be life-threatening or having high potential for organ system failure.
   */
  HighRisk: allergy_intolerance_criticality_HighRisk,
  /**
   * Worst case result of a future exposure is not assessed to be life-threatening or having high potential for organ system failure.
   */
  LowRisk: allergy_intolerance_criticality_LowRisk,
  /**
   * Unable to assess the worst case result of a future exposure.
   */
  UnableToAssessRisk: allergy_intolerance_criticality_UnableToAssessRisk,
};
const allergy_intolerance_type_Allergy: Coding = {
  code: 'allergy',
  display: 'Allergy',
  system: 'http://hl7.org/fhir/allergy-intolerance-type',
};
const allergy_intolerance_type_Intolerance: Coding = {
  code: 'intolerance',
  display: 'Intolerance',
  system: 'http://hl7.org/fhir/allergy-intolerance-type',
};
/**
 * Identification of the underlying physiological mechanism for a Reaction Risk.
 */
export const AllergyIntoleranceType = {
  /**
   * A propensity for hypersensitive reaction(s) to a substance.  These reactions are most typically type I hypersensitivity, plus other "allergy-like" reactions, including pseudoallergy.
   */
  Allergy: allergy_intolerance_type_Allergy,
  /**
   * A propensity for adverse reactions to a substance that is not judged to be allergic or "allergy-like".  These reactions are typically (but not necessarily) non-immune.  They are to some degree idiosyncratic and/or patient-specific (i.e. are not a reaction that is expected to occur with most or all patients given similar circumstances).
   */
  Intolerance: allergy_intolerance_type_Intolerance,
};
const allergyintolerance_verification_Confirmed: Coding = {
  code: 'confirmed',
  display: 'Confirmed',
  system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-verification',
};
const allergyintolerance_verification_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-verification',
};
const allergyintolerance_verification_Refuted: Coding = {
  code: 'refuted',
  display: 'Refuted',
  system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-verification',
};
const allergyintolerance_verification_Unconfirmed: Coding = {
  code: 'unconfirmed',
  display: 'Unconfirmed',
  system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-verification',
};
/**
 * Preferred value set for AllergyIntolerance Verification Status.
 */
export const AllergyintoleranceVerification = {
  /**
   * A high level of certainty about the propensity for a reaction to the identified substance, which may include clinical evidence by testing or rechallenge.
   */
  Confirmed: allergyintolerance_verification_Confirmed,
  /**
   * The statement was entered in error and is not valid.
   */
  EnteredInError: allergyintolerance_verification_EnteredInError,
  /**
   * A propensity for a reaction to the identified substance has been disputed or disproven with a sufficient level of clinical certainty to justify invalidating the assertion. This might or might not include testing or rechallenge.
   */
  Refuted: allergyintolerance_verification_Refuted,
  /**
   * A low level of certainty about the propensity for a reaction to the identified substance.
   */
  Unconfirmed: allergyintolerance_verification_Unconfirmed,
};
const resource_types_Account: Coding = {
  code: 'Account',
  display: 'Account',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ActivityDefinition: Coding = {
  code: 'ActivityDefinition',
  display: 'ActivityDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Address: Coding = {
  code: 'Address',
  display: 'Address',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_AdverseEvent: Coding = {
  code: 'AdverseEvent',
  display: 'AdverseEvent',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Age: Coding = {
  code: 'Age',
  display: 'Age',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_AllergyIntolerance: Coding = {
  code: 'AllergyIntolerance',
  display: 'AllergyIntolerance',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Annotation: Coding = {
  code: 'Annotation',
  display: 'Annotation',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Appointment: Coding = {
  code: 'Appointment',
  display: 'Appointment',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_AppointmentResponse: Coding = {
  code: 'AppointmentResponse',
  display: 'AppointmentResponse',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Attachment: Coding = {
  code: 'Attachment',
  display: 'Attachment',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_AuditEvent: Coding = {
  code: 'AuditEvent',
  display: 'AuditEvent',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_BackboneElement: Coding = {
  code: 'BackboneElement',
  display: 'BackboneElement',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Base64Binary: Coding = {
  code: 'base64Binary',
  display: 'base64Binary',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Basic: Coding = {
  code: 'Basic',
  display: 'Basic',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Binary: Coding = {
  code: 'Binary',
  display: 'Binary',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_BiologicallyDerivedProduct: Coding = {
  code: 'BiologicallyDerivedProduct',
  display: 'BiologicallyDerivedProduct',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_BodyStructure: Coding = {
  code: 'BodyStructure',
  display: 'BodyStructure',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Boolean: Coding = {
  code: 'boolean',
  display: 'boolean',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Bundle: Coding = {
  code: 'Bundle',
  display: 'Bundle',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Canonical: Coding = {
  code: 'canonical',
  display: 'canonical',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_CapabilityStatement: Coding = {
  code: 'CapabilityStatement',
  display: 'CapabilityStatement',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_CarePlan: Coding = {
  code: 'CarePlan',
  display: 'CarePlan',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_CareTeam: Coding = {
  code: 'CareTeam',
  display: 'CareTeam',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_CatalogEntry: Coding = {
  code: 'CatalogEntry',
  display: 'CatalogEntry',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ChargeItem: Coding = {
  code: 'ChargeItem',
  display: 'ChargeItem',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ChargeItemDefinition: Coding = {
  code: 'ChargeItemDefinition',
  display: 'ChargeItemDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Claim: Coding = {
  code: 'Claim',
  display: 'Claim',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ClaimResponse: Coding = {
  code: 'ClaimResponse',
  display: 'ClaimResponse',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ClinicalImpression: Coding = {
  code: 'ClinicalImpression',
  display: 'ClinicalImpression',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Code: Coding = {
  code: 'code',
  display: 'code',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_CodeableConcept: Coding = {
  code: 'CodeableConcept',
  display: 'CodeableConcept',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_CodeSystem: Coding = {
  code: 'CodeSystem',
  display: 'CodeSystem',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Coding: Coding = {
  code: 'Coding',
  display: 'Coding',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Communication: Coding = {
  code: 'Communication',
  display: 'Communication',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_CommunicationRequest: Coding = {
  code: 'CommunicationRequest',
  display: 'CommunicationRequest',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_CompartmentDefinition: Coding = {
  code: 'CompartmentDefinition',
  display: 'CompartmentDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Composition: Coding = {
  code: 'Composition',
  display: 'Composition',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ConceptMap: Coding = {
  code: 'ConceptMap',
  display: 'ConceptMap',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Condition: Coding = {
  code: 'Condition',
  display: 'Condition',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Consent: Coding = {
  code: 'Consent',
  display: 'Consent',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_ContactDetail: Coding = {
  code: 'ContactDetail',
  display: 'ContactDetail',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_ContactPoint: Coding = {
  code: 'ContactPoint',
  display: 'ContactPoint',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Contract: Coding = {
  code: 'Contract',
  display: 'Contract',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Contributor: Coding = {
  code: 'Contributor',
  display: 'Contributor',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Count: Coding = {
  code: 'Count',
  display: 'Count',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Coverage: Coding = {
  code: 'Coverage',
  display: 'Coverage',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_CoverageEligibilityRequest: Coding = {
  code: 'CoverageEligibilityRequest',
  display: 'CoverageEligibilityRequest',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_CoverageEligibilityResponse: Coding = {
  code: 'CoverageEligibilityResponse',
  display: 'CoverageEligibilityResponse',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_DataRequirement: Coding = {
  code: 'DataRequirement',
  display: 'DataRequirement',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Date: Coding = {
  code: 'date',
  display: 'date',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_DateTime: Coding = {
  code: 'dateTime',
  display: 'dateTime',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Decimal: Coding = {
  code: 'decimal',
  display: 'decimal',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_DetectedIssue: Coding = {
  code: 'DetectedIssue',
  display: 'DetectedIssue',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Device: Coding = {
  code: 'Device',
  display: 'Device',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_DeviceDefinition: Coding = {
  code: 'DeviceDefinition',
  display: 'DeviceDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_DeviceMetric: Coding = {
  code: 'DeviceMetric',
  display: 'DeviceMetric',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_DeviceRequest: Coding = {
  code: 'DeviceRequest',
  display: 'DeviceRequest',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_DeviceUseStatement: Coding = {
  code: 'DeviceUseStatement',
  display: 'DeviceUseStatement',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_DiagnosticReport: Coding = {
  code: 'DiagnosticReport',
  display: 'DiagnosticReport',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Distance: Coding = {
  code: 'Distance',
  display: 'Distance',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_DocumentManifest: Coding = {
  code: 'DocumentManifest',
  display: 'DocumentManifest',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_DocumentReference: Coding = {
  code: 'DocumentReference',
  display: 'DocumentReference',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_DomainResource: Coding = {
  code: 'DomainResource',
  display: 'DomainResource',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Dosage: Coding = {
  code: 'Dosage',
  display: 'Dosage',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Duration: Coding = {
  code: 'Duration',
  display: 'Duration',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_EffectEvidenceSynthesis: Coding = {
  code: 'EffectEvidenceSynthesis',
  display: 'EffectEvidenceSynthesis',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Element: Coding = {
  code: 'Element',
  display: 'Element',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_ElementDefinition: Coding = {
  code: 'ElementDefinition',
  display: 'ElementDefinition',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Encounter: Coding = {
  code: 'Encounter',
  display: 'Encounter',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Endpoint: Coding = {
  code: 'Endpoint',
  display: 'Endpoint',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_EnrollmentRequest: Coding = {
  code: 'EnrollmentRequest',
  display: 'EnrollmentRequest',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_EnrollmentResponse: Coding = {
  code: 'EnrollmentResponse',
  display: 'EnrollmentResponse',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_EpisodeOfCare: Coding = {
  code: 'EpisodeOfCare',
  display: 'EpisodeOfCare',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_EventDefinition: Coding = {
  code: 'EventDefinition',
  display: 'EventDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Evidence: Coding = {
  code: 'Evidence',
  display: 'Evidence',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_EvidenceVariable: Coding = {
  code: 'EvidenceVariable',
  display: 'EvidenceVariable',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ExampleScenario: Coding = {
  code: 'ExampleScenario',
  display: 'ExampleScenario',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ExplanationOfBenefit: Coding = {
  code: 'ExplanationOfBenefit',
  display: 'ExplanationOfBenefit',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Expression: Coding = {
  code: 'Expression',
  display: 'Expression',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Extension: Coding = {
  code: 'Extension',
  display: 'Extension',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_FamilyMemberHistory: Coding = {
  code: 'FamilyMemberHistory',
  display: 'FamilyMemberHistory',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Flag: Coding = {
  code: 'Flag',
  display: 'Flag',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Goal: Coding = {
  code: 'Goal',
  display: 'Goal',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_GraphDefinition: Coding = {
  code: 'GraphDefinition',
  display: 'GraphDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Group: Coding = {
  code: 'Group',
  display: 'Group',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_GuidanceResponse: Coding = {
  code: 'GuidanceResponse',
  display: 'GuidanceResponse',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_HealthcareService: Coding = {
  code: 'HealthcareService',
  display: 'HealthcareService',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_HumanName: Coding = {
  code: 'HumanName',
  display: 'HumanName',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Id: Coding = {
  code: 'id',
  display: 'id',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Identifier: Coding = {
  code: 'Identifier',
  display: 'Identifier',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_ImagingStudy: Coding = {
  code: 'ImagingStudy',
  display: 'ImagingStudy',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Immunization: Coding = {
  code: 'Immunization',
  display: 'Immunization',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ImmunizationEvaluation: Coding = {
  code: 'ImmunizationEvaluation',
  display: 'ImmunizationEvaluation',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ImmunizationRecommendation: Coding = {
  code: 'ImmunizationRecommendation',
  display: 'ImmunizationRecommendation',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ImplementationGuide: Coding = {
  code: 'ImplementationGuide',
  display: 'ImplementationGuide',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Instant: Coding = {
  code: 'instant',
  display: 'instant',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_InsurancePlan: Coding = {
  code: 'InsurancePlan',
  display: 'InsurancePlan',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Integer: Coding = {
  code: 'integer',
  display: 'integer',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Invoice: Coding = {
  code: 'Invoice',
  display: 'Invoice',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Library: Coding = {
  code: 'Library',
  display: 'Library',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Linkage: Coding = {
  code: 'Linkage',
  display: 'Linkage',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_List: Coding = {
  code: 'List',
  display: 'List',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Location: Coding = {
  code: 'Location',
  display: 'Location',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Markdown: Coding = {
  code: 'markdown',
  display: 'markdown',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_MarketingStatus: Coding = {
  code: 'MarketingStatus',
  display: 'MarketingStatus',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Measure: Coding = {
  code: 'Measure',
  display: 'Measure',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MeasureReport: Coding = {
  code: 'MeasureReport',
  display: 'MeasureReport',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Media: Coding = {
  code: 'Media',
  display: 'Media',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Medication: Coding = {
  code: 'Medication',
  display: 'Medication',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicationAdministration: Coding = {
  code: 'MedicationAdministration',
  display: 'MedicationAdministration',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicationDispense: Coding = {
  code: 'MedicationDispense',
  display: 'MedicationDispense',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicationKnowledge: Coding = {
  code: 'MedicationKnowledge',
  display: 'MedicationKnowledge',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicationRequest: Coding = {
  code: 'MedicationRequest',
  display: 'MedicationRequest',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicationStatement: Coding = {
  code: 'MedicationStatement',
  display: 'MedicationStatement',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicinalProduct: Coding = {
  code: 'MedicinalProduct',
  display: 'MedicinalProduct',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicinalProductAuthorization: Coding = {
  code: 'MedicinalProductAuthorization',
  display: 'MedicinalProductAuthorization',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicinalProductContraindication: Coding = {
  code: 'MedicinalProductContraindication',
  display: 'MedicinalProductContraindication',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicinalProductIndication: Coding = {
  code: 'MedicinalProductIndication',
  display: 'MedicinalProductIndication',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicinalProductIngredient: Coding = {
  code: 'MedicinalProductIngredient',
  display: 'MedicinalProductIngredient',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicinalProductInteraction: Coding = {
  code: 'MedicinalProductInteraction',
  display: 'MedicinalProductInteraction',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicinalProductManufactured: Coding = {
  code: 'MedicinalProductManufactured',
  display: 'MedicinalProductManufactured',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicinalProductPackaged: Coding = {
  code: 'MedicinalProductPackaged',
  display: 'MedicinalProductPackaged',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicinalProductPharmaceutical: Coding = {
  code: 'MedicinalProductPharmaceutical',
  display: 'MedicinalProductPharmaceutical',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MedicinalProductUndesirableEffect: Coding = {
  code: 'MedicinalProductUndesirableEffect',
  display: 'MedicinalProductUndesirableEffect',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MessageDefinition: Coding = {
  code: 'MessageDefinition',
  display: 'MessageDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_MessageHeader: Coding = {
  code: 'MessageHeader',
  display: 'MessageHeader',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Meta: Coding = {
  code: 'Meta',
  display: 'Meta',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_MolecularSequence: Coding = {
  code: 'MolecularSequence',
  display: 'MolecularSequence',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Money: Coding = {
  code: 'Money',
  display: 'Money',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_MoneyQuantity: Coding = {
  code: 'MoneyQuantity',
  display: 'MoneyQuantity',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_NamingSystem: Coding = {
  code: 'NamingSystem',
  display: 'NamingSystem',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Narrative: Coding = {
  code: 'Narrative',
  display: 'Narrative',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_NutritionOrder: Coding = {
  code: 'NutritionOrder',
  display: 'NutritionOrder',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Observation: Coding = {
  code: 'Observation',
  display: 'Observation',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ObservationDefinition: Coding = {
  code: 'ObservationDefinition',
  display: 'ObservationDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Oid: Coding = {
  code: 'oid',
  display: 'oid',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_OperationDefinition: Coding = {
  code: 'OperationDefinition',
  display: 'OperationDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_OperationOutcome: Coding = {
  code: 'OperationOutcome',
  display: 'OperationOutcome',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Organization: Coding = {
  code: 'Organization',
  display: 'Organization',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_OrganizationAffiliation: Coding = {
  code: 'OrganizationAffiliation',
  display: 'OrganizationAffiliation',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_ParameterDefinition: Coding = {
  code: 'ParameterDefinition',
  display: 'ParameterDefinition',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Parameters: Coding = {
  code: 'Parameters',
  display: 'Parameters',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Patient: Coding = {
  code: 'Patient',
  display: 'Patient',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_PaymentNotice: Coding = {
  code: 'PaymentNotice',
  display: 'PaymentNotice',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_PaymentReconciliation: Coding = {
  code: 'PaymentReconciliation',
  display: 'PaymentReconciliation',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Period: Coding = {
  code: 'Period',
  display: 'Period',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Person: Coding = {
  code: 'Person',
  display: 'Person',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_PlanDefinition: Coding = {
  code: 'PlanDefinition',
  display: 'PlanDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Population: Coding = {
  code: 'Population',
  display: 'Population',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_PositiveInt: Coding = {
  code: 'positiveInt',
  display: 'positiveInt',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Practitioner: Coding = {
  code: 'Practitioner',
  display: 'Practitioner',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_PractitionerRole: Coding = {
  code: 'PractitionerRole',
  display: 'PractitionerRole',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Procedure: Coding = {
  code: 'Procedure',
  display: 'Procedure',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_ProdCharacteristic: Coding = {
  code: 'ProdCharacteristic',
  display: 'ProdCharacteristic',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_ProductShelfLife: Coding = {
  code: 'ProductShelfLife',
  display: 'ProductShelfLife',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Provenance: Coding = {
  code: 'Provenance',
  display: 'Provenance',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Quantity: Coding = {
  code: 'Quantity',
  display: 'Quantity',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Questionnaire: Coding = {
  code: 'Questionnaire',
  display: 'Questionnaire',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_QuestionnaireResponse: Coding = {
  code: 'QuestionnaireResponse',
  display: 'QuestionnaireResponse',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Range: Coding = {
  code: 'Range',
  display: 'Range',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Ratio: Coding = {
  code: 'Ratio',
  display: 'Ratio',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Reference: Coding = {
  code: 'Reference',
  display: 'Reference',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_RelatedArtifact: Coding = {
  code: 'RelatedArtifact',
  display: 'RelatedArtifact',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_RelatedPerson: Coding = {
  code: 'RelatedPerson',
  display: 'RelatedPerson',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_RequestGroup: Coding = {
  code: 'RequestGroup',
  display: 'RequestGroup',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ResearchDefinition: Coding = {
  code: 'ResearchDefinition',
  display: 'ResearchDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ResearchElementDefinition: Coding = {
  code: 'ResearchElementDefinition',
  display: 'ResearchElementDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ResearchStudy: Coding = {
  code: 'ResearchStudy',
  display: 'ResearchStudy',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ResearchSubject: Coding = {
  code: 'ResearchSubject',
  display: 'ResearchSubject',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Resource: Coding = {
  code: 'Resource',
  display: 'Resource',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_RiskAssessment: Coding = {
  code: 'RiskAssessment',
  display: 'RiskAssessment',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_RiskEvidenceSynthesis: Coding = {
  code: 'RiskEvidenceSynthesis',
  display: 'RiskEvidenceSynthesis',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_SampledData: Coding = {
  code: 'SampledData',
  display: 'SampledData',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Schedule: Coding = {
  code: 'Schedule',
  display: 'Schedule',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_SearchParameter: Coding = {
  code: 'SearchParameter',
  display: 'SearchParameter',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_ServiceRequest: Coding = {
  code: 'ServiceRequest',
  display: 'ServiceRequest',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Signature: Coding = {
  code: 'Signature',
  display: 'Signature',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_SimpleQuantity: Coding = {
  code: 'SimpleQuantity',
  display: 'SimpleQuantity',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_Slot: Coding = {
  code: 'Slot',
  display: 'Slot',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Specimen: Coding = {
  code: 'Specimen',
  display: 'Specimen',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_SpecimenDefinition: Coding = {
  code: 'SpecimenDefinition',
  display: 'SpecimenDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_String: Coding = {
  code: 'string',
  display: 'string',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_StructureDefinition: Coding = {
  code: 'StructureDefinition',
  display: 'StructureDefinition',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_StructureMap: Coding = {
  code: 'StructureMap',
  display: 'StructureMap',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Subscription: Coding = {
  code: 'Subscription',
  display: 'Subscription',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Substance: Coding = {
  code: 'Substance',
  display: 'Substance',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_SubstanceAmount: Coding = {
  code: 'SubstanceAmount',
  display: 'SubstanceAmount',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_SubstanceNucleicAcid: Coding = {
  code: 'SubstanceNucleicAcid',
  display: 'SubstanceNucleicAcid',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_SubstancePolymer: Coding = {
  code: 'SubstancePolymer',
  display: 'SubstancePolymer',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_SubstanceProtein: Coding = {
  code: 'SubstanceProtein',
  display: 'SubstanceProtein',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_SubstanceReferenceInformation: Coding = {
  code: 'SubstanceReferenceInformation',
  display: 'SubstanceReferenceInformation',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_SubstanceSourceMaterial: Coding = {
  code: 'SubstanceSourceMaterial',
  display: 'SubstanceSourceMaterial',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_SubstanceSpecification: Coding = {
  code: 'SubstanceSpecification',
  display: 'SubstanceSpecification',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_SupplyDelivery: Coding = {
  code: 'SupplyDelivery',
  display: 'SupplyDelivery',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_SupplyRequest: Coding = {
  code: 'SupplyRequest',
  display: 'SupplyRequest',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_Task: Coding = {
  code: 'Task',
  display: 'Task',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_TerminologyCapabilities: Coding = {
  code: 'TerminologyCapabilities',
  display: 'TerminologyCapabilities',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_TestReport: Coding = {
  code: 'TestReport',
  display: 'TestReport',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_TestScript: Coding = {
  code: 'TestScript',
  display: 'TestScript',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_Time: Coding = {
  code: 'time',
  display: 'time',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Timing: Coding = {
  code: 'Timing',
  display: 'Timing',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_TriggerDefinition: Coding = {
  code: 'TriggerDefinition',
  display: 'TriggerDefinition',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_UnsignedInt: Coding = {
  code: 'unsignedInt',
  display: 'unsignedInt',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Uri: Coding = {
  code: 'uri',
  display: 'uri',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Url: Coding = {
  code: 'url',
  display: 'url',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_UsageContext: Coding = {
  code: 'UsageContext',
  display: 'UsageContext',
  system: 'http://hl7.org/fhir/data-types',
};
const data_types_Uuid: Coding = {
  code: 'uuid',
  display: 'uuid',
  system: 'http://hl7.org/fhir/data-types',
};
const resource_types_ValueSet: Coding = {
  code: 'ValueSet',
  display: 'ValueSet',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_VerificationResult: Coding = {
  code: 'VerificationResult',
  display: 'VerificationResult',
  system: 'http://hl7.org/fhir/resource-types',
};
const resource_types_VisionPrescription: Coding = {
  code: 'VisionPrescription',
  display: 'VisionPrescription',
  system: 'http://hl7.org/fhir/resource-types',
};
const data_types_XHTML: Coding = {
  code: 'xhtml',
  display: 'XHTML',
  system: 'http://hl7.org/fhir/data-types',
};
/**
 * A list of all the concrete types defined in this version of the FHIR specification - Abstract Types, Data Types and Resource Types.
 */
export const AllTypes = {
  /**
   * A financial tool for tracking value accrued for a particular purpose.  In the healthcare field, used to track charges for a patient, cost centers, etc.
   */
  Account_resource_types: resource_types_Account,
  /**
   * This resource allows for the definition of some activity to be performed, independent of a particular patient, practitioner, or other performance context.
   */
  ActivityDefinition_resource_types: resource_types_ActivityDefinition,
  /**
   * An address expressed using postal conventions (as opposed to GPS or other location definition formats).  This data type may be used to convey addresses for use in delivering mail as well as for visiting locations which might not be valid for mail delivery.  There are a variety of postal address formats defined around the world.
   */
  Address_data_types: data_types_Address,
  /**
   * Actual or  potential/avoided event causing unintended physical injury resulting from or contributed to by medical care, a research study or other healthcare setting factors that requires additional monitoring, treatment, or hospitalization, or that results in death.
   */
  AdverseEvent_resource_types: resource_types_AdverseEvent,
  /**
   * A duration of time during which an organism (or a process) has existed.
   */
  Age_data_types: data_types_Age,
  /**
   * Risk of harmful or undesirable, physiological response which is unique to an individual and associated with exposure to a substance.
   */
  AllergyIntolerance_resource_types: resource_types_AllergyIntolerance,
  /**
   * A  text note which also  contains information about who made the statement and when.
   */
  Annotation_data_types: data_types_Annotation,
  /**
   * A place holder that means any kind of resource
   */
  Any_abstract_types: abstract_types_Any,
  /**
   * A booking of a healthcare event among patient(s), practitioner(s), related person(s) and/or device(s) for a specific date/time. This may result in one or more Encounter(s).
   */
  Appointment_resource_types: resource_types_Appointment,
  /**
   * A reply to an appointment request for a patient and/or practitioner(s), such as a confirmation or rejection.
   */
  AppointmentResponse_resource_types: resource_types_AppointmentResponse,
  /**
   * For referring to data content defined in other formats.
   */
  Attachment_data_types: data_types_Attachment,
  /**
   * A record of an event made for purposes of maintaining a security log. Typical uses include detection of intrusion attempts and monitoring for inappropriate usage.
   */
  AuditEvent_resource_types: resource_types_AuditEvent,
  /**
   * Base definition for all elements that are defined inside a resource - but not those in a data type.
   */
  BackboneElement_data_types: data_types_BackboneElement,
  /**
   * A stream of bytes
   */
  Base64Binary_data_types: data_types_Base64Binary,
  /**
   * Basic is used for handling concepts not yet defined in FHIR, narrative-only resources that don't map to an existing resource, and custom resources not appropriate for inclusion in the FHIR specification.
   */
  Basic_resource_types: resource_types_Basic,
  /**
   * A resource that represents the data of a single raw artifact as digital content accessible in its native format.  A Binary resource can contain any content, whether text, image, pdf, zip archive, etc.
   */
  Binary_resource_types: resource_types_Binary,
  /**
   * A material substance originating from a biological entity intended to be transplanted or infused
   * into another (possibly the same) biological entity.
   */
  BiologicallyDerivedProduct_resource_types: resource_types_BiologicallyDerivedProduct,
  /**
   * Record details about an anatomical structure.  This resource may be used when a coded concept does not provide the necessary detail needed for the use case.
   */
  BodyStructure_resource_types: resource_types_BodyStructure,
  /**
   * Value of "true" or "false"
   */
  Boolean_data_types: data_types_Boolean,
  /**
   * A container for a collection of resources.
   */
  Bundle_resource_types: resource_types_Bundle,
  /**
   * A URI that is a reference to a canonical URL on a FHIR resource
   */
  Canonical_data_types: data_types_Canonical,
  /**
   * A Capability Statement documents a set of capabilities (behaviors) of a FHIR Server for a particular version of FHIR that may be used as a statement of actual server functionality or a statement of required or desired server implementation.
   */
  CapabilityStatement_resource_types: resource_types_CapabilityStatement,
  /**
   * Describes the intention of how one or more practitioners intend to deliver care for a particular patient, group or community for a period of time, possibly limited to care for a specific condition or set of conditions.
   */
  CarePlan_resource_types: resource_types_CarePlan,
  /**
   * The Care Team includes all the people and organizations who plan to participate in the coordination and delivery of care for a patient.
   */
  CareTeam_resource_types: resource_types_CareTeam,
  /**
   * Catalog entries are wrappers that contextualize items included in a catalog.
   */
  CatalogEntry_resource_types: resource_types_CatalogEntry,
  /**
   * The resource ChargeItem describes the provision of healthcare provider products for a certain patient, therefore referring not only to the product, but containing in addition details of the provision, like date, time, amounts and participating organizations and persons. Main Usage of the ChargeItem is to enable the billing process and internal cost allocation.
   */
  ChargeItem_resource_types: resource_types_ChargeItem,
  /**
   * The ChargeItemDefinition resource provides the properties that apply to the (billing) codes necessary to calculate costs and prices. The properties may differ largely depending on type and realm, therefore this resource gives only a rough structure and requires profiling for each type of billing code system.
   */
  ChargeItemDefinition_resource_types: resource_types_ChargeItemDefinition,
  /**
   * A provider issued list of professional services and products which have been provided, or are to be provided, to a patient which is sent to an insurer for reimbursement.
   */
  Claim_resource_types: resource_types_Claim,
  /**
   * This resource provides the adjudication details from the processing of a Claim resource.
   */
  ClaimResponse_resource_types: resource_types_ClaimResponse,
  /**
   * A record of a clinical assessment performed to determine what problem(s) may affect the patient and before planning the treatments or management strategies that are best to manage a patient's condition. Assessments are often 1:1 with a clinical consultation / encounter,  but this varies greatly depending on the clinical workflow. This resource is called "ClinicalImpression" rather than "ClinicalAssessment" to avoid confusion with the recording of assessment tools such as Apgar score.
   */
  ClinicalImpression_resource_types: resource_types_ClinicalImpression,
  /**
   * A string which has at least one character and no leading or trailing whitespace and where there is no whitespace other than single spaces in the contents
   */
  Code_data_types: data_types_Code,
  /**
   * A concept that may be defined by a formal reference to a terminology or ontology or may be provided by text.
   */
  CodeableConcept_data_types: data_types_CodeableConcept,
  /**
   * The CodeSystem resource is used to declare the existence of and describe a code system or code system supplement and its key properties, and optionally define a part or all of its content.
   */
  CodeSystem_resource_types: resource_types_CodeSystem,
  /**
   * A reference to a code defined by a terminology system.
   */
  Coding_data_types: data_types_Coding,
  /**
   * An occurrence of information being transmitted; e.g. an alert that was sent to a responsible provider, a public health agency that was notified about a reportable condition.
   */
  Communication_resource_types: resource_types_Communication,
  /**
   * A request to convey information; e.g. the CDS system proposes that an alert be sent to a responsible provider, the CDS system proposes that the public health agency be notified about a reportable condition.
   */
  CommunicationRequest_resource_types: resource_types_CommunicationRequest,
  /**
   * A compartment definition that defines how resources are accessed on a server.
   */
  CompartmentDefinition_resource_types: resource_types_CompartmentDefinition,
  /**
   * A set of healthcare-related information that is assembled together into a single logical package that provides a single coherent statement of meaning, establishes its own context and that has clinical attestation with regard to who is making the statement. A Composition defines the structure and narrative content necessary for a document. However, a Composition alone does not constitute a document. Rather, the Composition must be the first entry in a Bundle where Bundle.type=document, and any other resources referenced from Composition must be included as subsequent entries in the Bundle (for example Patient, Practitioner, Encounter, etc.).
   */
  Composition_resource_types: resource_types_Composition,
  /**
   * A statement of relationships from one set of concepts to one or more other concepts - either concepts in code systems, or data element/data element concepts, or classes in class models.
   */
  ConceptMap_resource_types: resource_types_ConceptMap,
  /**
   * A clinical condition, problem, diagnosis, or other event, situation, issue, or clinical concept that has risen to a level of concern.
   */
  Condition_resource_types: resource_types_Condition,
  /**
   * A record of a healthcare consumer’s  choices, which permits or denies identified recipient(s) or recipient role(s) to perform one or more actions within a given policy context, for specific purposes and periods of time.
   */
  Consent_resource_types: resource_types_Consent,
  /**
   * Specifies contact information for a person or organization.
   */
  ContactDetail_data_types: data_types_ContactDetail,
  /**
   * Details for all kinds of technology mediated contact points for a person or organization, including telephone, email, etc.
   */
  ContactPoint_data_types: data_types_ContactPoint,
  /**
   * Legally enforceable, formally recorded unilateral or bilateral directive i.e., a policy or agreement.
   */
  Contract_resource_types: resource_types_Contract,
  /**
   * A contributor to the content of a knowledge asset, including authors, editors, reviewers, and endorsers.
   */
  Contributor_data_types: data_types_Contributor,
  /**
   * A measured amount (or an amount that can potentially be measured). Note that measured amounts include amounts that are not precisely quantified, including amounts involving arbitrary units and floating currencies.
   */
  Count_data_types: data_types_Count,
  /**
   * Financial instrument which may be used to reimburse or pay for health care products and services. Includes both insurance and self-payment.
   */
  Coverage_resource_types: resource_types_Coverage,
  /**
   * The CoverageEligibilityRequest provides patient and insurance coverage information to an insurer for them to respond, in the form of an CoverageEligibilityResponse, with information regarding whether the stated coverage is valid and in-force and optionally to provide the insurance details of the policy.
   */
  CoverageEligibilityRequest_resource_types: resource_types_CoverageEligibilityRequest,
  /**
   * This resource provides eligibility and plan details from the processing of an CoverageEligibilityRequest resource.
   */
  CoverageEligibilityResponse_resource_types: resource_types_CoverageEligibilityResponse,
  /**
   * Describes a required data item for evaluation in terms of the type of data, and optional code or date-based filters of the data.
   */
  DataRequirement_data_types: data_types_DataRequirement,
  /**
   * A date or partial date (e.g. just year or year + month). There is no time zone. The format is a union of the schema types gYear, gYearMonth and date.  Dates SHALL be valid dates.
   */
  Date_data_types: data_types_Date,
  /**
   * A date, date-time or partial date (e.g. just year or year + month).  If hours and minutes are specified, a time zone SHALL be populated. The format is a union of the schema types gYear, gYearMonth, date and dateTime. Seconds must be provided due to schema type constraints but may be zero-filled and may be ignored.                 Dates SHALL be valid dates.
   */
  DateTime_data_types: data_types_DateTime,
  /**
   * A rational number with implicit precision
   */
  Decimal_data_types: data_types_Decimal,
  /**
   * Indicates an actual or potential clinical issue with or between one or more active or proposed clinical actions for a patient; e.g. Drug-drug interaction, Ineffective treatment frequency, Procedure-condition conflict, etc.
   */
  DetectedIssue_resource_types: resource_types_DetectedIssue,
  /**
   * A type of a manufactured item that is used in the provision of healthcare without being substantially changed through that activity. The device may be a medical or non-medical device.
   */
  Device_resource_types: resource_types_Device,
  /**
   * The characteristics, operational status and capabilities of a medical-related component of a medical device.
   */
  DeviceDefinition_resource_types: resource_types_DeviceDefinition,
  /**
   * Describes a measurement, calculation or setting capability of a medical device.
   */
  DeviceMetric_resource_types: resource_types_DeviceMetric,
  /**
   * Represents a request for a patient to employ a medical device. The device may be an implantable device, or an external assistive device, such as a walker.
   */
  DeviceRequest_resource_types: resource_types_DeviceRequest,
  /**
   * A record of a device being used by a patient where the record is the result of a report from the patient or another clinician.
   */
  DeviceUseStatement_resource_types: resource_types_DeviceUseStatement,
  /**
   * The findings and interpretation of diagnostic  tests performed on patients, groups of patients, devices, and locations, and/or specimens derived from these. The report includes clinical context such as requesting and provider information, and some mix of atomic results, images, textual and coded interpretations, and formatted representation of diagnostic reports.
   */
  DiagnosticReport_resource_types: resource_types_DiagnosticReport,
  /**
   * A length - a value with a unit that is a physical distance.
   */
  Distance_data_types: data_types_Distance,
  /**
   * A collection of documents compiled for a purpose together with metadata that applies to the collection.
   */
  DocumentManifest_resource_types: resource_types_DocumentManifest,
  /**
   * A reference to a document of any kind for any purpose. Provides metadata about the document so that the document can be discovered and managed. The scope of a document is any seralized object with a mime-type, so includes formal patient centric documents (CDA), cliical notes, scanned paper, and non-patient specific documents like policy text.
   */
  DocumentReference_resource_types: resource_types_DocumentReference,
  /**
   * A resource that includes narrative, extensions, and contained resources.
   */
  DomainResource_resource_types: resource_types_DomainResource,
  /**
   * Indicates how the medication is/was taken or should be taken by the patient.
   */
  Dosage_data_types: data_types_Dosage,
  /**
   * A length of time.
   */
  Duration_data_types: data_types_Duration,
  /**
   * The EffectEvidenceSynthesis resource describes the difference in an outcome between exposures states in a population where the effect estimate is derived from a combination of research studies.
   */
  EffectEvidenceSynthesis_resource_types: resource_types_EffectEvidenceSynthesis,
  /**
   * Base definition for all elements in a resource.
   */
  Element_data_types: data_types_Element,
  /**
   * Captures constraints on each element within the resource, profile, or extension.
   */
  ElementDefinition_data_types: data_types_ElementDefinition,
  /**
   * An interaction between a patient and healthcare provider(s) for the purpose of providing healthcare service(s) or assessing the health status of a patient.
   */
  Encounter_resource_types: resource_types_Encounter,
  /**
   * The technical details of an endpoint that can be used for electronic services, such as for web services providing XDS.b or a REST endpoint for another FHIR server. This may include any security context information.
   */
  Endpoint_resource_types: resource_types_Endpoint,
  /**
   * This resource provides the insurance enrollment details to the insurer regarding a specified coverage.
   */
  EnrollmentRequest_resource_types: resource_types_EnrollmentRequest,
  /**
   * This resource provides enrollment and plan details from the processing of an EnrollmentRequest resource.
   */
  EnrollmentResponse_resource_types: resource_types_EnrollmentResponse,
  /**
   * An association between a patient and an organization / healthcare provider(s) during which time encounters may occur. The managing organization assumes a level of responsibility for the patient during this time.
   */
  EpisodeOfCare_resource_types: resource_types_EpisodeOfCare,
  /**
   * The EventDefinition resource provides a reusable description of when a particular event can occur.
   */
  EventDefinition_resource_types: resource_types_EventDefinition,
  /**
   * The Evidence resource describes the conditional state (population and any exposures being compared within the population) and outcome (if specified) that the knowledge (evidence, assertion, recommendation) is about.
   */
  Evidence_resource_types: resource_types_Evidence,
  /**
   * The EvidenceVariable resource describes a "PICO" element that knowledge (evidence, assertion, recommendation) is about.
   */
  EvidenceVariable_resource_types: resource_types_EvidenceVariable,
  /**
   * Example of workflow instance.
   */
  ExampleScenario_resource_types: resource_types_ExampleScenario,
  /**
   * This resource provides: the claim details; adjudication details from the processing of a Claim; and optionally account balance information, for informing the subscriber of the benefits provided.
   */
  ExplanationOfBenefit_resource_types: resource_types_ExplanationOfBenefit,
  /**
   * A expression that is evaluated in a specified context and returns a value. The context of use of the expression must specify the context in which the expression is evaluated, and how the result of the expression is used.
   */
  Expression_data_types: data_types_Expression,
  /**
   * Optional Extension Element - found in all resources.
   */
  Extension_data_types: data_types_Extension,
  /**
   * Significant health conditions for a person related to the patient relevant in the context of care for the patient.
   */
  FamilyMemberHistory_resource_types: resource_types_FamilyMemberHistory,
  /**
   * Prospective warnings of potential issues when providing care to the patient.
   */
  Flag_resource_types: resource_types_Flag,
  /**
   * Describes the intended objective(s) for a patient, group or organization care, for example, weight loss, restoring an activity of daily living, obtaining herd immunity via immunization, meeting a process improvement objective, etc.
   */
  Goal_resource_types: resource_types_Goal,
  /**
   * A formal computable definition of a graph of resources - that is, a coherent set of resources that form a graph by following references. The Graph Definition resource defines a set and makes rules about the set.
   */
  GraphDefinition_resource_types: resource_types_GraphDefinition,
  /**
   * Represents a defined collection of entities that may be discussed or acted upon collectively but which are not expected to act collectively, and are not formally or legally recognized; i.e. a collection of entities that isn't an Organization.
   */
  Group_resource_types: resource_types_Group,
  /**
   * A guidance response is the formal response to a guidance request, including any output parameters returned by the evaluation, as well as the description of any proposed actions to be taken.
   */
  GuidanceResponse_resource_types: resource_types_GuidanceResponse,
  /**
   * The details of a healthcare service available at a location.
   */
  HealthcareService_resource_types: resource_types_HealthcareService,
  /**
   * A human's name with the ability to identify parts and usage.
   */
  HumanName_data_types: data_types_HumanName,
  /**
   * Any combination of letters, numerals, "-" and ".", with a length limit of 64 characters.  (This might be an integer, an unprefixed OID, UUID or any other identifier pattern that meets these constraints.)  Ids are case-insensitive.
   */
  Id_data_types: data_types_Id,
  /**
   * An identifier - identifies some entity uniquely and unambiguously. Typically this is used for business identifiers.
   */
  Identifier_data_types: data_types_Identifier,
  /**
   * Representation of the content produced in a DICOM imaging study. A study comprises a set of series, each of which includes a set of Service-Object Pair Instances (SOP Instances - images or other data) acquired or produced in a common context.  A series is of only one modality (e.g. X-ray, CT, MR, ultrasound), but a study may have multiple series of different modalities.
   */
  ImagingStudy_resource_types: resource_types_ImagingStudy,
  /**
   * Describes the event of a patient being administered a vaccine or a record of an immunization as reported by a patient, a clinician or another party.
   */
  Immunization_resource_types: resource_types_Immunization,
  /**
   * Describes a comparison of an immunization event against published recommendations to determine if the administration is "valid" in relation to those  recommendations.
   */
  ImmunizationEvaluation_resource_types: resource_types_ImmunizationEvaluation,
  /**
   * A patient's point-in-time set of recommendations (i.e. forecasting) according to a published schedule with optional supporting justification.
   */
  ImmunizationRecommendation_resource_types: resource_types_ImmunizationRecommendation,
  /**
   * A set of rules of how a particular interoperability or standards problem is solved - typically through the use of FHIR resources. This resource is used to gather all the parts of an implementation guide into a logical whole and to publish a computable definition of all the parts.
   */
  ImplementationGuide_resource_types: resource_types_ImplementationGuide,
  /**
   * An instant in time - known at least to the second
   */
  Instant_data_types: data_types_Instant,
  /**
   * Details of a Health Insurance product/plan provided by an organization.
   */
  InsurancePlan_resource_types: resource_types_InsurancePlan,
  /**
   * A whole number
   */
  Integer_data_types: data_types_Integer,
  /**
   * Invoice containing collected ChargeItems from an Account with calculated individual and total price for Billing purpose.
   */
  Invoice_resource_types: resource_types_Invoice,
  /**
   * The Library resource is a general-purpose container for knowledge asset definitions. It can be used to describe and expose existing knowledge assets such as logic libraries and information model descriptions, as well as to describe a collection of knowledge assets.
   */
  Library_resource_types: resource_types_Library,
  /**
   * Identifies two or more records (resource instances) that refer to the same real-world "occurrence".
   */
  Linkage_resource_types: resource_types_Linkage,
  /**
   * A list is a curated collection of resources.
   */
  List_resource_types: resource_types_List,
  /**
   * Details and position information for a physical place where services are provided and resources and participants may be stored, found, contained, or accommodated.
   */
  Location_resource_types: resource_types_Location,
  /**
   * A string that may contain Github Flavored Markdown syntax for optional processing by a mark down presentation engine
   */
  Markdown_data_types: data_types_Markdown,
  /**
   * The marketing status describes the date when a medicinal product is actually put on the market or the date as of which it is no longer available.
   */
  MarketingStatus_data_types: data_types_MarketingStatus,
  /**
   * The Measure resource provides the definition of a quality measure.
   */
  Measure_resource_types: resource_types_Measure,
  /**
   * The MeasureReport resource contains the results of the calculation of a measure; and optionally a reference to the resources involved in that calculation.
   */
  MeasureReport_resource_types: resource_types_MeasureReport,
  /**
   * A photo, video, or audio recording acquired or used in healthcare. The actual content may be inline or provided by direct reference.
   */
  Media_resource_types: resource_types_Media,
  /**
   * This resource is primarily used for the identification and definition of a medication for the purposes of prescribing, dispensing, and administering a medication as well as for making statements about medication use.
   */
  Medication_resource_types: resource_types_Medication,
  /**
   * Describes the event of a patient consuming or otherwise being administered a medication.  This may be as simple as swallowing a tablet or it may be a long running infusion.  Related resources tie this event to the authorizing prescription, and the specific encounter between patient and health care practitioner.
   */
  MedicationAdministration_resource_types: resource_types_MedicationAdministration,
  /**
   * Indicates that a medication product is to be or has been dispensed for a named person/patient.  This includes a description of the medication product (supply) provided and the instructions for administering the medication.  The medication dispense is the result of a pharmacy system responding to a medication order.
   */
  MedicationDispense_resource_types: resource_types_MedicationDispense,
  /**
   * Information about a medication that is used to support knowledge.
   */
  MedicationKnowledge_resource_types: resource_types_MedicationKnowledge,
  /**
   * An order or request for both supply of the medication and the instructions for administration of the medication to a patient. The resource is called "MedicationRequest" rather than "MedicationPrescription" or "MedicationOrder" to generalize the use across inpatient and outpatient settings, including care plans, etc., and to harmonize with workflow patterns.
   */
  MedicationRequest_resource_types: resource_types_MedicationRequest,
  /**
   * A record of a medication that is being consumed by a patient.   A MedicationStatement may indicate that the patient may be taking the medication now or has taken the medication in the past or will be taking the medication in the future.  The source of this information can be the patient, significant other (such as a family member or spouse), or a clinician.  A common scenario where this information is captured is during the history taking process during a patient visit or stay.   The medication information may come from sources such as the patient's memory, from a prescription bottle,  or from a list of medications the patient, clinician or other party maintains.
   * The primary difference between a medication statement and a medication administration is that the medication administration has complete administration information and is based on actual administration information from the person who administered the medication.  A medication statement is often, if not always, less specific.  There is no required date/time when the medication was administered, in fact we only know that a source has reported the patient is taking this medication, where details such as time, quantity, or rate or even medication product may be incomplete or missing or less precise.  As stated earlier, the medication statement information may come from the patient's memory, from a prescription bottle or from a list of medications the patient, clinician or other party maintains.  Medication administration is more formal and is not missing detailed information.
   */
  MedicationStatement_resource_types: resource_types_MedicationStatement,
  /**
   * Detailed definition of a medicinal product, typically for uses other than direct patient care (e.g. regulatory use).
   */
  MedicinalProduct_resource_types: resource_types_MedicinalProduct,
  /**
   * The regulatory authorization of a medicinal product.
   */
  MedicinalProductAuthorization_resource_types: resource_types_MedicinalProductAuthorization,
  /**
   * The clinical particulars - indications, contraindications etc. of a medicinal product, including for regulatory purposes.
   */
  MedicinalProductContraindication_resource_types: resource_types_MedicinalProductContraindication,
  /**
   * Indication for the Medicinal Product.
   */
  MedicinalProductIndication_resource_types: resource_types_MedicinalProductIndication,
  /**
   * An ingredient of a manufactured item or pharmaceutical product.
   */
  MedicinalProductIngredient_resource_types: resource_types_MedicinalProductIngredient,
  /**
   * The interactions of the medicinal product with other medicinal products, or other forms of interactions.
   */
  MedicinalProductInteraction_resource_types: resource_types_MedicinalProductInteraction,
  /**
   * The manufactured item as contained in the packaged medicinal product.
   */
  MedicinalProductManufactured_resource_types: resource_types_MedicinalProductManufactured,
  /**
   * A medicinal product in a container or package.
   */
  MedicinalProductPackaged_resource_types: resource_types_MedicinalProductPackaged,
  /**
   * A pharmaceutical product described in terms of its composition and dose form.
   */
  MedicinalProductPharmaceutical_resource_types: resource_types_MedicinalProductPharmaceutical,
  /**
   * Describe the undesirable effects of the medicinal product.
   */
  MedicinalProductUndesirableEffect_resource_types: resource_types_MedicinalProductUndesirableEffect,
  /**
   * Defines the characteristics of a message that can be shared between systems, including the type of event that initiates the message, the content to be transmitted and what response(s), if any, are permitted.
   */
  MessageDefinition_resource_types: resource_types_MessageDefinition,
  /**
   * The header for a message exchange that is either requesting or responding to an action.  The reference(s) that are the subject of the action as well as other information related to the action are typically transmitted in a bundle in which the MessageHeader resource instance is the first resource in the bundle.
   */
  MessageHeader_resource_types: resource_types_MessageHeader,
  /**
   * The metadata about a resource. This is content in the resource that is maintained by the infrastructure. Changes to the content might not always be associated with version changes to the resource.
   */
  Meta_data_types: data_types_Meta,
  /**
   * Raw data describing a biological sequence.
   */
  MolecularSequence_resource_types: resource_types_MolecularSequence,
  /**
   * An amount of economic utility in some recognized currency.
   */
  Money_data_types: data_types_Money,
  MoneyQuantity_data_types: data_types_MoneyQuantity,
  /**
   * A curated namespace that issues unique symbols within that namespace for the identification of concepts, people, devices, etc.  Represents a "System" used within the Identifier and Coding data types.
   */
  NamingSystem_resource_types: resource_types_NamingSystem,
  /**
   * A human-readable summary of the resource conveying the essential clinical and business information for the resource.
   */
  Narrative_data_types: data_types_Narrative,
  /**
   * A request to supply a diet, formula feeding (enteral) or oral nutritional supplement to a patient/resident.
   */
  NutritionOrder_resource_types: resource_types_NutritionOrder,
  /**
   * Measurements and simple assertions made about a patient, device or other subject.
   */
  Observation_resource_types: resource_types_Observation,
  /**
   * Set of definitional characteristics for a kind of observation or measurement produced or consumed by an orderable health care service.
   */
  ObservationDefinition_resource_types: resource_types_ObservationDefinition,
  /**
   * An OID represented as a URI
   */
  Oid_data_types: data_types_Oid,
  /**
   * A formal computable definition of an operation (on the RESTful interface) or a named query (using the search interaction).
   */
  OperationDefinition_resource_types: resource_types_OperationDefinition,
  /**
   * A collection of error, warning, or information messages that result from a system action.
   */
  OperationOutcome_resource_types: resource_types_OperationOutcome,
  /**
   * A formally or informally recognized grouping of people or organizations formed for the purpose of achieving some form of collective action.  Includes companies, institutions, corporations, departments, community groups, healthcare practice groups, payer/insurer, etc.
   */
  Organization_resource_types: resource_types_Organization,
  /**
   * Defines an affiliation/assotiation/relationship between 2 distinct oganizations, that is not a part-of relationship/sub-division relationship.
   */
  OrganizationAffiliation_resource_types: resource_types_OrganizationAffiliation,
  /**
   * The parameters to the module. This collection specifies both the input and output parameters. Input parameters are provided by the caller as part of the $evaluate operation. Output parameters are included in the GuidanceResponse.
   */
  ParameterDefinition_data_types: data_types_ParameterDefinition,
  /**
   * This resource is a non-persisted resource used to pass information into and back from an [operation](operations.html). It has no other use, and there is no RESTful endpoint associated with it.
   */
  Parameters_resource_types: resource_types_Parameters,
  /**
   * Demographics and other administrative information about an individual or animal receiving care or other health-related services.
   */
  Patient_resource_types: resource_types_Patient,
  /**
   * This resource provides the status of the payment for goods and services rendered, and the request and response resource references.
   */
  PaymentNotice_resource_types: resource_types_PaymentNotice,
  /**
   * This resource provides the details including amount of a payment and allocates the payment items being paid.
   */
  PaymentReconciliation_resource_types: resource_types_PaymentReconciliation,
  /**
   * A time period defined by a start and end date and optionally time.
   */
  Period_data_types: data_types_Period,
  /**
   * Demographics and administrative information about a person independent of a specific health-related context.
   */
  Person_resource_types: resource_types_Person,
  /**
   * This resource allows for the definition of various types of plans as a sharable, consumable, and executable artifact. The resource is general enough to support the description of a broad range of clinical artifacts such as clinical decision support rules, order sets and protocols.
   */
  PlanDefinition_resource_types: resource_types_PlanDefinition,
  /**
   * A populatioof people with some set of grouping criteria.
   */
  Population_data_types: data_types_Population,
  /**
   * An integer with a value that is positive (e.g. >0)
   */
  PositiveInt_data_types: data_types_PositiveInt,
  /**
   * A person who is directly or indirectly involved in the provisioning of healthcare.
   */
  Practitioner_resource_types: resource_types_Practitioner,
  /**
   * A specific set of Roles/Locations/specialties/services that a practitioner may perform at an organization for a period of time.
   */
  PractitionerRole_resource_types: resource_types_PractitionerRole,
  /**
   * An action that is or was performed on or for a patient. This can be a physical intervention like an operation, or less invasive like long term services, counseling, or hypnotherapy.
   */
  Procedure_resource_types: resource_types_Procedure,
  /**
   * The marketing status describes the date when a medicinal product is actually put on the market or the date as of which it is no longer available.
   */
  ProdCharacteristic_data_types: data_types_ProdCharacteristic,
  /**
   * The shelf-life and storage information for a medicinal product item or container can be described using this class.
   */
  ProductShelfLife_data_types: data_types_ProductShelfLife,
  /**
   * Provenance of a resource is a record that describes entities and processes involved in producing and delivering or otherwise influencing that resource. Provenance provides a critical foundation for assessing authenticity, enabling trust, and allowing reproducibility. Provenance assertions are a form of contextual metadata and can themselves become important records with their own provenance. Provenance statement indicates clinical significance in terms of confidence in authenticity, reliability, and trustworthiness, integrity, and stage in lifecycle (e.g. Document Completion - has the artifact been legally authenticated), all of which may impact security, privacy, and trust policies.
   */
  Provenance_resource_types: resource_types_Provenance,
  /**
   * A measured amount (or an amount that can potentially be measured). Note that measured amounts include amounts that are not precisely quantified, including amounts involving arbitrary units and floating currencies.
   */
  Quantity_data_types: data_types_Quantity,
  /**
   * A structured set of questions intended to guide the collection of answers from end-users. Questionnaires provide detailed control over order, presentation, phraseology and grouping to allow coherent, consistent data collection.
   */
  Questionnaire_resource_types: resource_types_Questionnaire,
  /**
   * A structured set of questions and their answers. The questions are ordered and grouped into coherent subsets, corresponding to the structure of the grouping of the questionnaire being responded to.
   */
  QuestionnaireResponse_resource_types: resource_types_QuestionnaireResponse,
  /**
   * A set of ordered Quantities defined by a low and high limit.
   */
  Range_data_types: data_types_Range,
  /**
   * A relationship of two Quantity values - expressed as a numerator and a denominator.
   */
  Ratio_data_types: data_types_Ratio,
  /**
   * A reference from one resource to another.
   */
  Reference_data_types: data_types_Reference,
  /**
   * Related artifacts such as additional documentation, justification, or bibliographic references.
   */
  RelatedArtifact_data_types: data_types_RelatedArtifact,
  /**
   * Information about a person that is involved in the care for a patient, but who is not the target of healthcare, nor has a formal responsibility in the care process.
   */
  RelatedPerson_resource_types: resource_types_RelatedPerson,
  /**
   * A group of related requests that can be used to capture intended activities that have inter-dependencies such as "give this medication after that one".
   */
  RequestGroup_resource_types: resource_types_RequestGroup,
  /**
   * The ResearchDefinition resource describes the conditional state (population and any exposures being compared within the population) and outcome (if specified) that the knowledge (evidence, assertion, recommendation) is about.
   */
  ResearchDefinition_resource_types: resource_types_ResearchDefinition,
  /**
   * The ResearchElementDefinition resource describes a "PICO" element that knowledge (evidence, assertion, recommendation) is about.
   */
  ResearchElementDefinition_resource_types: resource_types_ResearchElementDefinition,
  /**
   * A process where a researcher or organization plans and then executes a series of steps intended to increase the field of healthcare-related knowledge.  This includes studies of safety, efficacy, comparative effectiveness and other information about medications, devices, therapies and other interventional and investigative techniques.  A ResearchStudy involves the gathering of information about human or animal subjects.
   */
  ResearchStudy_resource_types: resource_types_ResearchStudy,
  /**
   * A physical entity which is the primary unit of operational and/or administrative interest in a study.
   */
  ResearchSubject_resource_types: resource_types_ResearchSubject,
  /**
   * This is the base resource type for everything.
   */
  Resource_resource_types: resource_types_Resource,
  /**
   * An assessment of the likely outcome(s) for a patient or other subject as well as the likelihood of each outcome.
   */
  RiskAssessment_resource_types: resource_types_RiskAssessment,
  /**
   * The RiskEvidenceSynthesis resource describes the likelihood of an outcome in a population plus exposure state where the risk estimate is derived from a combination of research studies.
   */
  RiskEvidenceSynthesis_resource_types: resource_types_RiskEvidenceSynthesis,
  /**
   * A series of measurements taken by a device, with upper and lower limits. There may be more than one dimension in the data.
   */
  SampledData_data_types: data_types_SampledData,
  /**
   * A container for slots of time that may be available for booking appointments.
   */
  Schedule_resource_types: resource_types_Schedule,
  /**
   * A search parameter that defines a named search item that can be used to search/filter on a resource.
   */
  SearchParameter_resource_types: resource_types_SearchParameter,
  /**
   * A record of a request for service such as diagnostic investigations, treatments, or operations to be performed.
   */
  ServiceRequest_resource_types: resource_types_ServiceRequest,
  /**
   * A signature along with supporting context. The signature may be a digital signature that is cryptographic in nature, or some other signature acceptable to the domain. This other signature may be as simple as a graphical image representing a hand-written signature, or a signature ceremony Different signature approaches have different utilities.
   */
  Signature_data_types: data_types_Signature,
  SimpleQuantity_data_types: data_types_SimpleQuantity,
  /**
   * A slot of time on a schedule that may be available for booking appointments.
   */
  Slot_resource_types: resource_types_Slot,
  /**
   * A sample to be used for analysis.
   */
  Specimen_resource_types: resource_types_Specimen,
  /**
   * A kind of specimen with associated set of requirements.
   */
  SpecimenDefinition_resource_types: resource_types_SpecimenDefinition,
  /**
   * A sequence of Unicode characters
   */
  String_data_types: data_types_String,
  /**
   * A definition of a FHIR structure. This resource is used to describe the underlying resources, data types defined in FHIR, and also for describing extensions and constraints on resources and data types.
   */
  StructureDefinition_resource_types: resource_types_StructureDefinition,
  /**
   * A Map of relationships between 2 structures that can be used to transform data.
   */
  StructureMap_resource_types: resource_types_StructureMap,
  /**
   * The subscription resource is used to define a push-based subscription from a server to another system. Once a subscription is registered with the server, the server checks every resource that is created or updated, and if the resource matches the given criteria, it sends a message on the defined "channel" so that another system can take an appropriate action.
   */
  Subscription_resource_types: resource_types_Subscription,
  /**
   * A homogeneous material with a definite composition.
   */
  Substance_resource_types: resource_types_Substance,
  /**
   * Chemical substances are a single substance type whose primary defining element is the molecular structure. Chemical substances shall be defined on the basis of their complete covalent molecular structure; the presence of a salt (counter-ion) and/or solvates (water, alcohols) is also captured. Purity, grade, physical form or particle size are not taken into account in the definition of a chemical substance or in the assignment of a Substance ID.
   */
  SubstanceAmount_data_types: data_types_SubstanceAmount,
  /**
   * Nucleic acids are defined by three distinct elements: the base, sugar and linkage. Individual substance/moiety IDs will be created for each of these elements. The nucleotide sequence will be always entered in the 5’-3’ direction.
   */
  SubstanceNucleicAcid_resource_types: resource_types_SubstanceNucleicAcid,
  /**
   * Todo.
   */
  SubstancePolymer_resource_types: resource_types_SubstancePolymer,
  /**
   * A SubstanceProtein is defined as a single unit of a linear amino acid sequence, or a combination of subunits that are either covalently linked or have a defined invariant stoichiometric relationship. This includes all synthetic, recombinant and purified SubstanceProteins of defined sequence, whether the use is therapeutic or prophylactic. This set of elements will be used to describe albumins, coagulation factors, cytokines, growth factors, peptide/SubstanceProtein hormones, enzymes, toxins, toxoids, recombinant vaccines, and immunomodulators.
   */
  SubstanceProtein_resource_types: resource_types_SubstanceProtein,
  /**
   * Todo.
   */
  SubstanceReferenceInformation_resource_types: resource_types_SubstanceReferenceInformation,
  /**
   * Source material shall capture information on the taxonomic and anatomical origins as well as the fraction of a material that can result in or can be modified to form a substance. This set of data elements shall be used to define polymer substances isolated from biological matrices. Taxonomic and anatomical origins shall be described using a controlled vocabulary as required. This information is captured for naturally derived polymers ( . starch) and structurally diverse substances. For Organisms belonging to the Kingdom Plantae the Substance level defines the fresh material of a single species or infraspecies, the Herbal Drug and the Herbal preparation. For Herbal preparations, the fraction information will be captured at the Substance information level and additional information for herbal extracts will be captured at the Specified Substance Group 1 information level. See for further explanation the Substance Class: Structurally Diverse and the herbal annex.
   */
  SubstanceSourceMaterial_resource_types: resource_types_SubstanceSourceMaterial,
  /**
   * The detailed description of a substance, typically at a level beyond what is used for prescribing.
   */
  SubstanceSpecification_resource_types: resource_types_SubstanceSpecification,
  /**
   * Record of delivery of what is supplied.
   */
  SupplyDelivery_resource_types: resource_types_SupplyDelivery,
  /**
   * A record of a request for a medication, substance or device used in the healthcare setting.
   */
  SupplyRequest_resource_types: resource_types_SupplyRequest,
  /**
   * A task to be performed.
   */
  Task_resource_types: resource_types_Task,
  /**
   * A TerminologyCapabilities resource documents a set of capabilities (behaviors) of a FHIR Terminology Server that may be used as a statement of actual server functionality or a statement of required or desired server implementation.
   */
  TerminologyCapabilities_resource_types: resource_types_TerminologyCapabilities,
  /**
   * A summary of information based on the results of executing a TestScript.
   */
  TestReport_resource_types: resource_types_TestReport,
  /**
   * A structured set of tests against a FHIR server or client implementation to determine compliance against the FHIR specification.
   */
  TestScript_resource_types: resource_types_TestScript,
  /**
   * A time during the day, with no date specified
   */
  Time_data_types: data_types_Time,
  /**
   * Specifies an event that may occur multiple times. Timing schedules are used to record when things are planned, expected or requested to occur. The most common usage is in dosage instructions for medications. They are also used when planning care of various kinds, and may be used for reporting the schedule to which past regular activities were carried out.
   */
  Timing_data_types: data_types_Timing,
  /**
   * A description of a triggering event. Triggering events can be named events, data events, or periodic, as determined by the type element.
   */
  TriggerDefinition_data_types: data_types_TriggerDefinition,
  /**
   * A place holder that means any kind of data type
   */
  Type_abstract_types: abstract_types_Type,
  /**
   * An integer with a value that is not negative (e.g. >= 0)
   */
  UnsignedInt_data_types: data_types_UnsignedInt,
  /**
   * String of characters used to identify a name or a resource
   */
  Uri_data_types: data_types_Uri,
  /**
   * A URI that is a literal reference
   */
  Url_data_types: data_types_Url,
  /**
   * Specifies clinical/business/etc. metadata that can be used to retrieve, index and/or categorize an artifact. This metadata can either be specific to the applicable population (e.g., age category, DRG) or the specific context of care (e.g., venue, care setting, provider of care).
   */
  UsageContext_data_types: data_types_UsageContext,
  /**
   * A UUID, represented as a URI
   */
  Uuid_data_types: data_types_Uuid,
  /**
   * A ValueSet resource instance specifies a set of codes drawn from one or more code systems, intended for use in a particular context. Value sets link between [[[CodeSystem]]] definitions and their use in [coded elements](terminologies.html).
   */
  ValueSet_resource_types: resource_types_ValueSet,
  /**
   * Describes validation requirements, source(s), status and dates for one or more elements.
   */
  VerificationResult_resource_types: resource_types_VerificationResult,
  /**
   * An authorization for the provision of glasses and/or contact lenses to a patient.
   */
  VisionPrescription_resource_types: resource_types_VisionPrescription,
  /**
   * XHTML format, as defined by W3C, but restricted usage (mainly, no active content)
   */
  XHTML_data_types: data_types_XHTML,
};
const AnimalBreeds_PercheronHorseOrganism: Coding = {
  code: '1006005',
  display: 'Percheron horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelgiumLandracePigOrganism: Coding = {
  code: '10261003',
  display: 'Belgium landrace pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArucanaChickenOrganism: Coding = {
  code: '10643004',
  display: 'Arucana chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DekalbHybridPigLine77Organism: Coding = {
  code: '11161001',
  display: 'Dekalb hybrid pig line 77 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VikingHorseOrganism: Coding = {
  code: '1118004',
  display: 'Viking horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GujaratiCattleBreedOrganism: Coding = {
  code: '112485003',
  display: 'Gujarati cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NoTailSheepOrganism: Coding = {
  code: '112486002',
  display: 'No-tail sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomneletSheepOrganism: Coding = {
  code: '112487006',
  display: 'Romnelet sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HackneyHorseOrganism: Coding = {
  code: '112488001',
  display: 'Hackney horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BerkshirePigOrganism: Coding = {
  code: '112489009',
  display: 'Berkshire pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DekalbHybridPigLine63Organism: Coding = {
  code: '112490000',
  display: 'Dekalb hybrid pig line 63 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RumplessChickenOrganism: Coding = {
  code: '112495005',
  display: 'Rumpless chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PilgrimGooseOrganism: Coding = {
  code: '112496006',
  display: 'Pilgrim goose (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DelawareChickenOrganism: Coding = {
  code: '11830008',
  display: 'Delaware chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KerryHillSheepOrganism: Coding = {
  code: '11967001',
  display: 'Kerry Hill sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MissouriFoxTrottingHorseOrganism: Coding = {
  code: '12360007',
  display: 'Missouri fox trotting horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwedishLandracePigOrganism: Coding = {
  code: '12407009',
  display: 'Swedish landrace pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedcapChickenOrganism: Coding = {
  code: '12419001',
  display: 'Redcap chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ClydesdaleHorseOrganism: Coding = {
  code: '1247002',
  display: 'Clydesdale horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HerefordCattleSuperbreedOrganism: Coding = {
  code: '125074003',
  display: 'Hereford cattle superbreed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EquusCaballusGmeliniOrganism: Coding = {
  code: '125084002',
  display: 'Equus caballus gmelini (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EquusCaballusGmeliniXEquusCaballusCaballusOrganism: Coding = {
  code: '125086000',
  display: 'Equus caballus gmelini X Equus caballus caballus (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BosFrontalisOrganism: Coding = {
  code: '125092006',
  display: 'Bos frontalis (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CapraHircusOrganism: Coding = {
  code: '125097000',
  display: 'Capra hircus (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OvisAriesOrganism: Coding = {
  code: '125099002',
  display: 'Ovis aries (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MerinoSheepSuperbreedOrganism: Coding = {
  code: '125101009',
  display: 'Merino sheep superbreed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AnasPlatyrhynchosOrganism: Coding = {
  code: '125102002',
  display: 'Anas platyrhynchos (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AnserAnserOrganism: Coding = {
  code: '125104001',
  display: 'Anser anser (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CrevecoeurChickenOrganism: Coding = {
  code: '12874007',
  display: 'Crevecoeur chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AfricanderCattleBreedOrganism: Coding = {
  code: '131426006',
  display: 'Africander cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AnkoleCattleBreedOrganism: Coding = {
  code: '131427002',
  display: 'Ankole cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AnkoleWatusiCattleBreedOrganism: Coding = {
  code: '131428007',
  display: 'Ankole-Watusi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BaladiCattleBreedOrganism: Coding = {
  code: '131429004',
  display: 'Baladi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelmontRedCattleBreedOrganism: Coding = {
  code: '131430009',
  display: 'Belmont Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BonsmaraCattleBreedOrganism: Coding = {
  code: '131431008',
  display: 'Bonsmara cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DamiettaCattleBreedOrganism: Coding = {
  code: '131432001',
  display: 'Damietta cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HorroCattleBreedOrganism: Coding = {
  code: '131433006',
  display: 'Horro cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KuriCattleBreedOrganism: Coding = {
  code: '131434000',
  display: 'Kuri cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NguniCattleBreedOrganism: Coding = {
  code: '131435004',
  display: 'Nguni cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PhilippineNativeCattleBreedOrganism: Coding = {
  code: '131436003',
  display: 'Philippine Native cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomagnolaCattleBreedOrganism: Coding = {
  code: '131437007',
  display: 'Romagnola cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SanheCattleBreedOrganism: Coding = {
  code: '131438002',
  display: 'Sanhe cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TswanaCattleBreedOrganism: Coding = {
  code: '131439005',
  display: 'Tswana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TuliCattleBreedOrganism: Coding = {
  code: '131440007',
  display: 'Tuli cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AliabDinkaCattleBreedOrganism: Coding = {
  code: '131441006',
  display: 'Aliab Dinka cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlurCattleBreedOrganism: Coding = {
  code: '131442004',
  display: 'Alur cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AnkinaCattleBreedOrganism: Coding = {
  code: '131443009',
  display: 'Ankina cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ApulianPodolianCattleBreedOrganism: Coding = {
  code: '131444003',
  display: 'Apulian Podolian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AradoCattleBreedOrganism: Coding = {
  code: '131445002',
  display: 'Arado cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AweilDinkaCattleBreedOrganism: Coding = {
  code: '131446001',
  display: 'Aweil Dinka cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BahimaCattleBreedOrganism: Coding = {
  code: '131447005',
  display: 'Bahima cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BapediCattleBreedOrganism: Coding = {
  code: '131448000',
  display: 'Bapedi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BariaVietnamMadagascarCattleBreedOrganism: Coding = {
  code: '131449008',
  display: 'Baria (Vietnam/Madagascar) cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BarotseCattleBreedOrganism: Coding = {
  code: '131450008',
  display: 'Barotse cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BarraDoCuanzoCattleBreedOrganism: Coding = {
  code: '131451007',
  display: 'Barra do Cuanzo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BashiCattleBreedOrganism: Coding = {
  code: '131452000',
  display: 'Bashi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BasutoCattleBreedOrganism: Coding = {
  code: '131453005',
  display: 'Basuto cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BatangasCattleBreedOrganism: Coding = {
  code: '131454004',
  display: 'Batangas cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BavendaCattleBreedOrganism: Coding = {
  code: '131455003',
  display: 'Bavenda cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BejaCattleBreedOrganism: Coding = {
  code: '131456002',
  display: 'Beja cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CalabrianCattleBreedOrganism: Coding = {
  code: '131457006',
  display: 'Calabrian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlondeDuCapBonCattleBreedOrganism: Coding = {
  code: '131458001',
  display: 'Blonde-du Cap Bon cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChanDocCattleBreedOrganism: Coding = {
  code: '131459009',
  display: 'Chan-Doc cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChernigovCattleBreedOrganism: Coding = {
  code: '131460004',
  display: 'Chernigov cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChinoSantandereanoCattleBreedOrganism: Coding = {
  code: '131461000',
  display: 'Chino Santandereano cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CinisaraCattleBreedOrganism: Coding = {
  code: '131462007',
  display: 'Cinisara cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CupremHybridCattleBreedOrganism: Coding = {
  code: '131463002',
  display: 'Cuprem Hybrid cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DabieshanCattleBreedOrganism: Coding = {
  code: '131464008',
  display: 'Dabieshan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DamaraCattleBreedOrganism: Coding = {
  code: '131465009',
  display: 'Damara cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanakilCattleBreedOrganism: Coding = {
  code: '131466005',
  display: 'Danakil cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DnieperCattleBreedOrganism: Coding = {
  code: '131467001',
  display: 'Dnieper cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DoayoCattleBreedOrganism: Coding = {
  code: '131468006',
  display: 'Doayo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EasternNuerCattleBreedOrganism: Coding = {
  code: '131469003',
  display: 'Eastern Nuer cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EgyptianCattleBreedOrganism: Coding = {
  code: '131470002',
  display: 'Egyptian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FogeraCattleBreedOrganism: Coding = {
  code: '131471003',
  display: 'Fogera cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GarfagninaCattleBreedOrganism: Coding = {
  code: '131472005',
  display: 'Garfagnina cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GratiCattleBreedOrganism: Coding = {
  code: '131473000',
  display: 'Grati cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GaunlingCattleBreedOrganism: Coding = {
  code: '131474006',
  display: 'Gaunling cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HalhinGolCattleBreedOrganism: Coding = {
  code: '131475007',
  display: 'Halhin Gol cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HolmongerCattleBreedOrganism: Coding = {
  code: '131476008',
  display: 'Holmonger cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IlocosCattleBreedOrganism: Coding = {
  code: '131477004',
  display: 'Ilocos cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IloiloCattleBreedOrganism: Coding = {
  code: '131478009',
  display: 'Iloilo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_InkukuCattleBreedOrganism: Coding = {
  code: '131479001',
  display: 'Inkuku cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IskarCattleBreedOrganism: Coding = {
  code: '131480003',
  display: 'Iskar cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IstrianCattleBreedOrganism: Coding = {
  code: '131481004',
  display: 'Istrian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JavaneseOngoleCattleBreedOrganism: Coding = {
  code: '131482006',
  display: 'Javanese Ongole cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JavaneseZebuCattleBreedOrganism: Coding = {
  code: '131483001',
  display: 'Javanese Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JinnanCattleBreedOrganism: Coding = {
  code: '131484007',
  display: 'Jinnan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KalmykCattleBreedOrganism: Coding = {
  code: '131485008',
  display: 'Kalmyk cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KaokoveldCattleBreedOrganism: Coding = {
  code: '131486009',
  display: 'Kaokoveld cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KazakhWhiteheadCattleBreedOrganism: Coding = {
  code: '131487000',
  display: 'Kazakh Whitehead cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KedahKelantanCattleBreedOrganism: Coding = {
  code: '131488005',
  display: 'Kedah-Kelantan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KigeziCattleBreedOrganism: Coding = {
  code: '131489002',
  display: 'Kigezi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KisantuCattleBreedOrganism: Coding = {
  code: '131490006',
  display: 'Kisantu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KolubaraCattleBreedOrganism: Coding = {
  code: '131491005',
  display: 'Kolubara cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KurganCattleBreedOrganism: Coding = {
  code: '131492003',
  display: 'Kurgan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KyogaCattleBreedOrganism: Coding = {
  code: '131493008',
  display: 'Kyoga cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LucanianCattleBreedOrganism: Coding = {
  code: '131494002',
  display: 'Lucanian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MaremmanaCattleBreedOrganism: Coding = {
  code: '131495001',
  display: 'Maremmana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MarianasCattleBreedOrganism: Coding = {
  code: '131496000',
  display: 'Marianas cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MaryutiCattleBreedOrganism: Coding = {
  code: '131497009',
  display: 'Maryuti cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MauritiusCroleCattleBreedOrganism: Coding = {
  code: '131498004',
  display: 'Mauritius Créole cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MenufiCattleBreedOrganism: Coding = {
  code: '131499007',
  display: 'Menufi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MezzalinaCattleBreedOrganism: Coding = {
  code: '131500003',
  display: 'Mezzalina cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ModicanaCattleBreedOrganism: Coding = {
  code: '131501004',
  display: 'Modicana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MoiCattleBreedOrganism: Coding = {
  code: '131502006',
  display: 'Moi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NamaCattleBreedOrganism: Coding = {
  code: '131503001',
  display: 'Nama cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NanyangCattleBreedOrganism: Coding = {
  code: '131504007',
  display: 'Nanyang cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NQuoteDamaSangaCattleBreedOrganism: Coding = {
  code: '131505008',
  display: "N'Dama Sanga cattle breed (organism)",
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NgandaCattleBreedOrganism: Coding = {
  code: '131506009',
  display: 'Nganda cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NiloticSangaCattleBreedOrganism: Coding = {
  code: '131507000',
  display: 'Nilotic Sanga cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NkoneCattleBreedOrganism: Coding = {
  code: '131508005',
  display: 'Nkone cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorthMalawiAngoniCattleBreedOrganism: Coding = {
  code: '131509002',
  display: 'North Malawi Angoni cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NuerCattleBreedOrganism: Coding = {
  code: '131510007',
  display: 'Nuer cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NurasCattleBreedOrganism: Coding = {
  code: '131511006',
  display: 'Nuras cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NyoroCattleBreedOrganism: Coding = {
  code: '131512004',
  display: 'Nyoro cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OvamboCattleBreedOrganism: Coding = {
  code: '131513009',
  display: 'Ovambo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PantelleriaCattleBreedOrganism: Coding = {
  code: '131514003',
  display: 'Pantelleria cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PinzhouCattleBreedOrganism: Coding = {
  code: '131515002',
  display: 'Pinzhou cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PortoAmboimCattleBreedOrganism: Coding = {
  code: '131516001',
  display: 'Porto Amboim cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PosavinaCattleBreedOrganism: Coding = {
  code: '131517005',
  display: 'Posavina cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomanianSteppeCattleBreedOrganism: Coding = {
  code: '131518000',
  display: 'Romanian Steppe cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SaidiCattleBreedOrganism: Coding = {
  code: '131519008',
  display: 'Saidi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SardoModicanaCattleBreedOrganism: Coding = {
  code: '131520002',
  display: 'Sardo-Modicana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SengologaCattleBreedOrganism: Coding = {
  code: '131521003',
  display: 'Sengologa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SerereCattleBreedOrganism: Coding = {
  code: '131522005',
  display: 'Serere cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SeshagaCattleBreedOrganism: Coding = {
  code: '131523000',
  display: 'Seshaga cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SiberianBlackPiedCattleBreedOrganism: Coding = {
  code: '131524006',
  display: 'Siberian Black Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SocotraCattleBreedOrganism: Coding = {
  code: '131525007',
  display: 'Socotra cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthernTswanaCattleBreedOrganism: Coding = {
  code: '131526008',
  display: 'Southern Tswana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SprecaCattleBreedOrganism: Coding = {
  code: '131527004',
  display: 'Spreca cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SunkumaCattleBreedOrganism: Coding = {
  code: '131528009',
  display: 'Sunkuma cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TaiwanZebuCattleBreedOrganism: Coding = {
  code: '131529001',
  display: 'Taiwan Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThaiCattleBreedOrganism: Coding = {
  code: '131530006',
  display: 'Thai cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThailandFightingZebuCattleBreedOrganism: Coding = {
  code: '131531005',
  display: 'Thailand Fighting Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThanhHoaCattleBreedOrganism: Coding = {
  code: '131532003',
  display: 'Thanh-Hoa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TibetanCattleBreedOrganism: Coding = {
  code: '131533008',
  display: 'Tibetan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TongaCattleBreedOrganism: Coding = {
  code: '131534002',
  display: 'Tonga cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ToroCattleBreedOrganism: Coding = {
  code: '131535001',
  display: 'Toro cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TuniCattleBreedOrganism: Coding = {
  code: '131536000',
  display: 'Tuni cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TurkishGraySteppeCattleBreedOrganism: Coding = {
  code: '131537009',
  display: 'Turkish Gray Steppe cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TuyHoaCattleBreedOrganism: Coding = {
  code: '131538004',
  display: 'Tuy-Hoa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_UjumqinCattleBreedOrganism: Coding = {
  code: '131539007',
  display: 'Ujumqin cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AbigarCattleBreedOrganism: Coding = {
  code: '131540009',
  display: 'Abigar cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AfricangusCattleBreedOrganism: Coding = {
  code: '131541008',
  display: 'Africangus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AgeroleseCattleBreedOrganism: Coding = {
  code: '131542001',
  display: 'Agerolese cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlbeseCattleBreedOrganism: Coding = {
  code: '131543006',
  display: 'Albese cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_UkrainianGrayCattleBreedOrganism: Coding = {
  code: '131544000',
  display: 'Ukrainian Gray cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VietnameseYellowCattleBreedOrganism: Coding = {
  code: '131545004',
  display: 'Vietnamese Yellow cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WatusiUnitedStatesOfAmericaCattleBreedOrganism: Coding = {
  code: '131546003',
  display: 'Watusi (United States of America) cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WenshanCattleBreedOrganism: Coding = {
  code: '131547007',
  display: 'Wenshan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YakutCattleBreedOrganism: Coding = {
  code: '131548002',
  display: 'Yakut cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YunnanZebuCattleBreedOrganism: Coding = {
  code: '131549005',
  display: 'Yunnan Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ZambiaAngoniCattleBreedOrganism: Coding = {
  code: '131550005',
  display: 'Zambia Angoni cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DrakensbergerCattleBreedOrganism: Coding = {
  code: '131551009',
  display: 'Drakensberger cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ModicanaLowlandCattleBreedOrganism: Coding = {
  code: '131552002',
  display: 'Modicana lowland cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TaiwanYellowCattleBreedOrganism: Coding = {
  code: '131553007',
  display: 'Taiwan Yellow cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MengguCattleBreedOrganism: Coding = {
  code: '131554001',
  display: 'Menggu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlbresCattleBreedOrganism: Coding = {
  code: '131555000',
  display: 'Albères cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlentejanaCattleBreedOrganism: Coding = {
  code: '131556004',
  display: 'Alentejana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanWhiteParkCattleBreedOrganism: Coding = {
  code: '131557008',
  display: 'American White Park cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmerifaxcattleBreedOrganism: Coding = {
  code: '131558003',
  display: 'Amerifaxcattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AnatolianBlackCattleBreedOrganism: Coding = {
  code: '131559006',
  display: 'Anatolian Black cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AndalusianBlackCattleBreedOrganism: Coding = {
  code: '131560001',
  display: 'Andalusian Black cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SpanishChickenOrganism: Coding = {
  code: '13156006',
  display: 'Spanish chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AndalusianGrayCattleBreedOrganism: Coding = {
  code: '131561002',
  display: 'Andalusian Gray cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AngelnCattleBreedOrganism: Coding = {
  code: '131562009',
  display: 'Angeln cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AsturianMountainCattleBreedOrganism: Coding = {
  code: '131563004',
  display: 'Asturian Mountain cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AsturianValleyCattleBreedOrganism: Coding = {
  code: '131564005',
  display: 'Asturian Valley cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AubracCattleBreedOrganism: Coding = {
  code: '131565006',
  display: 'Aubrac cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AulieAtaCattleBreedOrganism: Coding = {
  code: '131566007',
  display: 'Aulie-Ata cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustralianLowlineCattleBreedOrganism: Coding = {
  code: '131567003',
  display: 'Australian Lowline cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BarzonaCattleBreedOrganism: Coding = {
  code: '131568008',
  display: 'Barzona cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BazadaisCattleBreedOrganism: Coding = {
  code: '131569000',
  display: 'Bazadais cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BeefmakerCattleBreedOrganism: Coding = {
  code: '131570004',
  display: 'Beefmaker cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelarusRedCattleBreedOrganism: Coding = {
  code: '131571000',
  display: 'Belarus Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelgianBlueCattleBreedOrganism: Coding = {
  code: '131572007',
  display: 'Belgian Blue cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelgianRedCattleBreedOrganism: Coding = {
  code: '131573002',
  display: 'Belgian Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelmontAdaptaurCattleBreedOrganism: Coding = {
  code: '131574008',
  display: 'Belmont Adaptaur cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BerrendasCattleBreedOrganism: Coding = {
  code: '131575009',
  display: 'Berrendas cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlacksidedTrondheimAndNorlandCattleBreedOrganism: Coding = {
  code: '131576005',
  display: 'Blacksided Trondheim and Norland cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlancoOrejinegroCattleBreedOrganism: Coding = {
  code: '131577001',
  display: 'Blanco Orejinegro cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BraunviehCattleBreedOrganism: Coding = {
  code: '131578006',
  display: 'Braunvieh cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BritishWhiteCattleBreedOrganism: Coding = {
  code: '131579003',
  display: 'British White cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CachenaCattleBreedOrganism: Coding = {
  code: '131580000',
  display: 'Cachena cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanaryIslandCattleBreedOrganism: Coding = {
  code: '131581001',
  display: 'Canary Island cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CarinthianBlondCattleBreedOrganism: Coding = {
  code: '131582008',
  display: 'Carinthian Blond cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CaucasianCattleBreedOrganism: Coding = {
  code: '131583003',
  display: 'Caucasian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CharolaisCattleBreedOrganism: Coding = {
  code: '131584009',
  display: 'Charolais cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChineseBlackAndWhiteCattleBreedOrganism: Coding = {
  code: '131585005',
  display: 'Chinese Black-and-White cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CorrienteCattleBreedOrganism: Coding = {
  code: '131586006',
  display: 'Corriente cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CosteoConCuernosCattleBreedOrganism: Coding = {
  code: '131587002',
  display: 'Costeño con Cuernos cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DamascusCattleBreedOrganism: Coding = {
  code: '131588007',
  display: 'Damascus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanishRedCattleBreedOrganism: Coding = {
  code: '131589004',
  display: 'Danish Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DevonCattleBreedOrganism: Coding = {
  code: '131590008',
  display: 'Devon cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DlafeCattleBreedOrganism: Coding = {
  code: '131591007',
  display: 'Dølafe cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DutchBeltedCattleBreedOrganism: Coding = {
  code: '131592000',
  display: 'Dutch Belted cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DutchFriesianCattleBreedOrganism: Coding = {
  code: '131593005',
  display: 'Dutch Friesian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EnglishLonghornCattleBreedOrganism: Coding = {
  code: '131594004',
  display: 'English Longhorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EstonianRedCattleBreedOrganism: Coding = {
  code: '131595003',
  display: 'Estonian Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EvolneCattleBreedOrganism: Coding = {
  code: '131596002',
  display: 'Evolène cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FightingBullCattleBreedOrganism: Coding = {
  code: '131597006',
  display: 'Fighting Bull cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FjallCattleBreedOrganism: Coding = {
  code: '131598001',
  display: 'Fjall cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FloridaCrackerPineywoodsCattleBreedOrganism: Coding = {
  code: '131599009',
  display: 'Florida Cracker/Pineywoods cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GalicianBlondCattleBreedOrganism: Coding = {
  code: '131600007',
  display: 'Galician Blond cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GasconCattleBreedOrganism: Coding = {
  code: '131601006',
  display: 'Gascon cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanRedPiedCattleBreedOrganism: Coding = {
  code: '131602004',
  display: 'German Red Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GlanCattleBreedOrganism: Coding = {
  code: '131603009',
  display: 'Glan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GloucesterCattleBreedOrganism: Coding = {
  code: '131604003',
  display: 'Gloucester cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GroningenWhiteheadedCattleBreedOrganism: Coding = {
  code: '131605002',
  display: 'Groningen Whiteheaded cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HartnCattleBreedOrganism: Coding = {
  code: '131606001',
  display: 'Hartón cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MixedBreedGoatOrganism: Coding = {
  code: '131607005',
  display: 'Mixed Breed Goat (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustralianGoatBreedOrganism: Coding = {
  code: '131608000',
  display: 'Australian Goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArapawaIslandGoatBreedOrganism: Coding = {
  code: '131609008',
  display: 'Arapawa Island goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MalteseGoatBreedOrganism: Coding = {
  code: '131610003',
  display: 'Maltese goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ProvenaleGoatBreedOrganism: Coding = {
  code: '131611004',
  display: 'Provençale goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NegraSerranaGoatBreedOrganism: Coding = {
  code: '131612006',
  display: 'Negra Serrana goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OrobicaGoatBreedOrganism: Coding = {
  code: '131613001',
  display: 'Orobica goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RoyaVesubieGoatBreedOrganism: Coding = {
  code: '131614007',
  display: 'Roya-Vesubie goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RetintaExtremenaGoatBreedOrganism: Coding = {
  code: '131615008',
  display: 'Retinta Extremena goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AppenzellGoatBreedOrganism: Coding = {
  code: '131616009',
  display: 'Appenzell goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanCashmereGoatBreedOrganism: Coding = {
  code: '131617000',
  display: 'American Cashmere goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AltaiMountainGoatBreedOrganism: Coding = {
  code: '131618005',
  display: 'Altai Mountain goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PyreneanGoatBreedOrganism: Coding = {
  code: '131619002',
  display: 'Pyrenean goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BagotGoatBreedOrganism: Coding = {
  code: '131620008',
  display: 'Bagot goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RussianWhiteGoatBreedOrganism: Coding = {
  code: '131621007',
  display: 'Russian White goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MoxotGoatBreedOrganism: Coding = {
  code: '131622000',
  display: 'Moxotó goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MyotonicGoatBreedOrganism: Coding = {
  code: '131623005',
  display: 'Myotonic goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NachiGoatBreedOrganism: Coding = {
  code: '131624004',
  display: 'Nachi goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NigerianDwarfGoatBreedOrganism: Coding = {
  code: '131625003',
  display: 'Nigerian Dwarf goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SardaGoatBreedOrganism: Coding = {
  code: '131626002',
  display: 'Sarda goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SerpentinaGoatBreedOrganism: Coding = {
  code: '131627006',
  display: 'Serpentina goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SerranaGoatBreedOrganism: Coding = {
  code: '131628001',
  display: 'Serrana goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VerataGoatBreedOrganism: Coding = {
  code: '131629009',
  display: 'Verata goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VerzascaBlackGoatBreedOrganism: Coding = {
  code: '131630004',
  display: 'Verzasca black goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorwegianGoatBreedOrganism: Coding = {
  code: '131631000',
  display: 'Norwegian goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OberhasliGoatBreedOrganism: Coding = {
  code: '131632007',
  display: 'Oberhasli goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PeacockGoatBreedOrganism: Coding = {
  code: '131633002',
  display: 'Peacock Goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PhilippineGoatBreedOrganism: Coding = {
  code: '131634008',
  display: 'Philippine goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LoashanGoatBreedOrganism: Coding = {
  code: '131635009',
  display: 'Loashan goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SanClementeGoatBreedOrganism: Coding = {
  code: '131636005',
  display: 'San Clemente goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SomaliGoatBreedOrganism: Coding = {
  code: '131637001',
  display: 'Somali goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SpanishGoatBreedOrganism: Coding = {
  code: '131638006',
  display: 'Spanish goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RoveGoatBreedOrganism: Coding = {
  code: '131639003',
  display: 'Rove goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SRDGoatBreedOrganism: Coding = {
  code: '131640001',
  display: 'SRD goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwedishLandraceGoatBreedOrganism: Coding = {
  code: '131641002',
  display: 'Swedish Landrace goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThuringianGoatBreedOrganism: Coding = {
  code: '131642009',
  display: 'Thuringian goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_UzbekBlackGoatBreedOrganism: Coding = {
  code: '131643004',
  display: 'Uzbek Black goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ZhongweiGoatBreedOrganism: Coding = {
  code: '131644005',
  display: 'Zhongwei goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BarbariGoatBreedOrganism: Coding = {
  code: '131645006',
  display: 'Barbari goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PoitouGoatBreedOrganism: Coding = {
  code: '131646007',
  display: 'Poitou goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RepartidaGoatBreedOrganism: Coding = {
  code: '131647003',
  display: 'Repartida goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BootedGoatBreedOrganism: Coding = {
  code: '131648008',
  display: 'Booted Goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CorsicanGoatBreedOrganism: Coding = {
  code: '131649000',
  display: 'Corsican goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChaparGoatBreedOrganism: Coding = {
  code: '131650000',
  display: 'Chapar goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanindGoatBreedOrganism: Coding = {
  code: '131651001',
  display: 'Canindé goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanaryIslandGoatBreedOrganism: Coding = {
  code: '131652008',
  display: 'Canary Island goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DaeraDinPanahGoatBreedOrganism: Coding = {
  code: '131653003',
  display: 'Daera Din Panah goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BritishAlpineGoatBreedOrganism: Coding = {
  code: '131654009',
  display: 'British Alpine goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BhujGoatBreedOrganism: Coding = {
  code: '131655005',
  display: 'Bhuj goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoerGoatBreedOrganism: Coding = {
  code: '131656006',
  display: 'Boer goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BenadirGoatBreedOrganism: Coding = {
  code: '131657002',
  display: 'Benadir goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CroleAntillesGoatBreedOrganism: Coding = {
  code: '131658007',
  display: 'Créole Antilles goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BeetalGoatBreedOrganism: Coding = {
  code: '131659004',
  display: 'Beetal goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GoldenGuernseyGoatBreedOrganism: Coding = {
  code: '131660009',
  display: 'Golden Guernsey goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanishLandraceGoatBreedOrganism: Coding = {
  code: '131661008',
  display: 'Danish Landrace goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KaghaniGoatBreedOrganism: Coding = {
  code: '131662001',
  display: 'Kaghani goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IrishGoatBreedOrganism: Coding = {
  code: '131663006',
  display: 'Irish Goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GrisonsStripedGoatBreedOrganism: Coding = {
  code: '131664000',
  display: 'Grisons Striped goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JiningGrayGoatBreedOrganism: Coding = {
  code: '131665004',
  display: 'Jining Gray goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FinnishLandraceGoatBreedOrganism: Coding = {
  code: '131666003',
  display: 'Finnish Landrace goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ErzgebirgGoatBreedOrganism: Coding = {
  code: '131667007',
  display: 'Erzgebirg goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KamoriGoatBreedOrganism: Coding = {
  code: '131668002',
  display: 'Kamori goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DonGoatBreedOrganism: Coding = {
  code: '131669005',
  display: 'Don goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KikoGoatBreedOrganism: Coding = {
  code: '131670006',
  display: 'Kiko goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KinderGoatBreedOrganism: Coding = {
  code: '131671005',
  display: 'Kinder goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PygoraGoatBreedOrganism: Coding = {
  code: '131672003',
  display: 'Pygora goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WoodenLegGoatBreedOrganism: Coding = {
  code: '131673008',
  display: 'Wooden Leg goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlpineChamoiseeGoatBreedOrganism: Coding = {
  code: '131674002',
  display: 'Alpine Chamoisee goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MassifCentralGoatBreedOrganism: Coding = {
  code: '131675001',
  display: 'Massif Central goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MalagueaGoatBreedOrganism: Coding = {
  code: '131676000',
  display: 'Malagueña goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlgarviaGoatBreedOrganism: Coding = {
  code: '131677009',
  display: 'Algarvia goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BritishSaanenGoatBreedOrganism: Coding = {
  code: '131678004',
  display: 'British Saanen goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BritishToggenburgGoatBreedOrganism: Coding = {
  code: '131679007',
  display: 'British Toggenburg goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BndnerGoatBreedOrganism: Coding = {
  code: '131680005',
  display: 'Bündner goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlancaAndaluzaGoatBreedOrganism: Coding = {
  code: '131681009',
  display: 'Blanca Andaluza goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlancaCeltibericaGoatBreedOrganism: Coding = {
  code: '131682002',
  display: 'Blanca Celtiberica goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BraviaGoatBreedOrganism: Coding = {
  code: '131683007',
  display: 'Bravia goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackGrisonneGoatBreedOrganism: Coding = {
  code: '131684001',
  display: 'Black Grisonne goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChamoisOfTheAlpsGoatBreedOrganism: Coding = {
  code: '131685000',
  display: 'Chamois of the Alps goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CharnequeriaGoatBreedOrganism: Coding = {
  code: '131686004',
  display: 'Charnequeria goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CarpatheGoatBreedOrganism: Coding = {
  code: '131687008',
  display: 'Carpathe goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ColNoirDuValaisGoatBreedOrganism: Coding = {
  code: '131688003',
  display: 'Col Noir du Valais goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DamaniGoatBreedOrganism: Coding = {
  code: '131689006',
  display: 'Damani goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DesFossesCommunesDeLQuoteOuestGoatBreedOrganism: Coding = {
  code: '131690002',
  display: "Des Fosses (Communes de l'Ouest) goat breed (organism)",
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EnglishGoatBreedOrganism: Coding = {
  code: '131691003',
  display: 'English goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EnglishGuernseyGoatBreedOrganism: Coding = {
  code: '131692005',
  display: 'English Guernsey goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanColoredGoatBreedOrganism: Coding = {
  code: '131693000',
  display: 'German colored goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuadarramaGoatBreedOrganism: Coding = {
  code: '131694006',
  display: 'Guadarrama goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GarganicaGoatBreedOrganism: Coding = {
  code: '131695007',
  display: 'Garganica goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GirgentanaGoatBreedOrganism: Coding = {
  code: '131696008',
  display: 'Girgentana goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JonicaGoatBreedOrganism: Coding = {
  code: '131697004',
  display: 'Jonica goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MurcianaGranadinaGoatBreedOrganism: Coding = {
  code: '131698009',
  display: 'Murciana-Granadina goat breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BndnerOberlandSheepBreedOrganism: Coding = {
  code: '131699001',
  display: 'Bündner Oberland sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BritishMilkSheepBreedOrganism: Coding = {
  code: '131700000',
  display: 'British Milk Sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrillenschafSheepBreedOrganism: Coding = {
  code: '131701001',
  display: 'Brillenschaf sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrecknockHillCheviotSheepBreedOrganism: Coding = {
  code: '131702008',
  display: 'Brecknock Hill Cheviot sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CholistaniSheepBreedOrganism: Coding = {
  code: '131703003',
  display: 'Cholistani sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BibrikSheepBreedOrganism: Coding = {
  code: '131704009',
  display: 'Bibrik sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ColumbiaSheepBreedOrganism: Coding = {
  code: '131705005',
  display: 'Columbia sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackWelshMountainSheepBreedOrganism: Coding = {
  code: '131706006',
  display: 'Black Welsh Mountain Sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackheadPersianSheepBreedOrganism: Coding = {
  code: '131707002',
  display: 'Blackhead Persian sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BleuDuMaineSheepBreedOrganism: Coding = {
  code: '131708007',
  display: 'Bleu du Maine sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BluefacedLeicesterSheepBreedOrganism: Coding = {
  code: '131709004',
  display: 'Bluefaced Leicester sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BondSheepBreedOrganism: Coding = {
  code: '131710009',
  display: 'Bond sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BorderLeicesterSheepBreedOrganism: Coding = {
  code: '131711008',
  display: 'Border Leicester sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoreraySheepBreedOrganism: Coding = {
  code: '131712001',
  display: 'Boreray sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BovskaSheepBreedOrganism: Coding = {
  code: '131713006',
  display: 'Bovska sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BraunesBergschafSheepBreedOrganism: Coding = {
  code: '131714000',
  display: 'Braunes Bergschaf sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrazilianSomaliSheepBreedOrganism: Coding = {
  code: '131715004',
  display: 'Brazilian Somali sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BeulahSpeckledFaceSheepBreedOrganism: Coding = {
  code: '131716003',
  display: 'Beulah Speckled-Face sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DartmoorSheepBreedOrganism: Coding = {
  code: '131717007',
  display: 'Dartmoor sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FabrianeseSheepBreedOrganism: Coding = {
  code: '131718002',
  display: 'Fabrianese sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ExmoorHornSheepBreedOrganism: Coding = {
  code: '131719005',
  display: 'Exmoor Horn sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ElliottdaleSheepBreedOrganism: Coding = {
  code: '131720004',
  display: 'Elliottdale sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DrysdaleSheepBreedOrganism: Coding = {
  code: '131721000',
  display: 'Drysdale sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DorsetDownSheepBreedOrganism: Coding = {
  code: '131722007',
  display: 'Dorset Down sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanBlackheadedMuttonSheepBreedOrganism: Coding = {
  code: '131723002',
  display: 'German Blackheaded Mutton sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KookaSheepBreedOrganism: Coding = {
  code: '131724008',
  display: 'Kooka sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FriesianMilkSheepBreedOrganism: Coding = {
  code: '131725009',
  display: 'Friesian Milk Sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GansuAlpineFineWoolSheepBreedOrganism: Coding = {
  code: '131726005',
  display: 'Gansu Alpine Fine-wool sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanWhiteheadedMuttonSheepBreedOrganism: Coding = {
  code: '131727001',
  display: 'German Whiteheaded Mutton sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GraueGehoernteHeidschnuckeSheepBreedOrganism: Coding = {
  code: '131728006',
  display: 'Graue Gehoernte Heidschnucke sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HanSheepBreedOrganism: Coding = {
  code: '131729003',
  display: 'Han sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GromarkSheepBreedOrganism: Coding = {
  code: '131730008',
  display: 'Gromark sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GulfCoastNativeSheepBreedOrganism: Coding = {
  code: '131731007',
  display: 'Gulf Coast Native sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DorperSheepBreedOrganism: Coding = {
  code: '131732000',
  display: 'Dorper sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DevonClosewoolSheepBreedOrganism: Coding = {
  code: '131733005',
  display: 'Devon Closewool sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DeutschesBlaukoepfigesFleischschafSheepBreedOrganism: Coding = {
  code: '131734004',
  display: 'Deutsches Blaukoepfiges Fleischschaf sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DerbyshireGritstoneSheepBreedOrganism: Coding = {
  code: '131735003',
  display: 'Derbyshire Gritstone sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CoburgerFuchsschafSheepBreedOrganism: Coding = {
  code: '131736002',
  display: 'Coburger Fuchsschaf sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanishLandraceSheepBreedOrganism: Coding = {
  code: '131737006',
  display: 'Danish Landrace sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuteSheepBreedOrganism: Coding = {
  code: '131738001',
  display: 'Gute sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HampshireSheepBreedOrganism: Coding = {
  code: '131739009',
  display: 'Hampshire sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GentileDiPugliaSheepBreedOrganism: Coding = {
  code: '131740006',
  display: 'Gentile di Puglia sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanMountainSheepBreedOrganism: Coding = {
  code: '131741005',
  display: 'German Mountain sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LuzeinSheepBreedOrganism: Coding = {
  code: '131742003',
  display: 'Luzein sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KatahdinSheepBreedOrganism: Coding = {
  code: '131743008',
  display: 'Katahdin sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LeineschafSheepBreedOrganism: Coding = {
  code: '131744002',
  display: 'Leineschaf sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LincolnLongwoolSheepBreedOrganism: Coding = {
  code: '131745001',
  display: 'Lincoln Longwool sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LlanwenogSheepBreedOrganism: Coding = {
  code: '131746000',
  display: 'Llanwenog sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LleynSheepBreedOrganism: Coding = {
  code: '131747009',
  display: 'Lleyn sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DamaraSheepBreedOrganism: Coding = {
  code: '131748004',
  display: 'Damara sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DamaniSheepBreedOrganism: Coding = {
  code: '131749007',
  display: 'Damani sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DalesbredSheepBreedOrganism: Coding = {
  code: '131750007',
  display: 'Dalesbred sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DalaSheepBreedOrganism: Coding = {
  code: '131751006',
  display: 'Dala sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CriolloSheepBreedOrganism: Coding = {
  code: '131752004',
  display: 'Criollo sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CormoSheepBreedOrganism: Coding = {
  code: '131753009',
  display: 'Cormo sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LatiSheepBreedOrganism: Coding = {
  code: '131754003',
  display: 'Lati sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LonkSheepBreedOrganism: Coding = {
  code: '131755002',
  display: 'Lonk sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LangheSheepBreedOrganism: Coding = {
  code: '131756001',
  display: 'Langhe sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ManxLoaghtanSheepBreedOrganism: Coding = {
  code: '131757005',
  display: 'Manx Loaghtan sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MasaiSheepBreedOrganism: Coding = {
  code: '131758000',
  display: 'Masai sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MerinolandschafSheepBreedOrganism: Coding = {
  code: '131759008',
  display: 'Merinolandschaf sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LohiSheepBreedOrganism: Coding = {
  code: '131760003',
  display: 'Lohi sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IleDeFranceSheepBreedOrganism: Coding = {
  code: '131761004',
  display: 'Ile-de-France sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HashtNagriSheepBreedOrganism: Coding = {
  code: '131762006',
  display: 'Hasht Nagri sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HazaragieSheepBreedOrganism: Coding = {
  code: '131763001',
  display: 'Hazaragie sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CoopworthSheepBreedOrganism: Coding = {
  code: '131764007',
  display: 'Coopworth sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ComisanaSheepBreedOrganism: Coding = {
  code: '131765008',
  display: 'Comisana sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ComebackSheepBreedOrganism: Coding = {
  code: '131766009',
  display: 'Comeback sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SicilianBarbarySheepBreedOrganism: Coding = {
  code: '131767000',
  display: 'Sicilian Barbary sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AfricanaSheepBreedOrganism: Coding = {
  code: '131768005',
  display: 'Africana sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WelshMountainBadgerFacedSheepBreedOrganism: Coding = {
  code: '131769002',
  display: 'Welsh Mountain Badger Faced sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HebrideanSheepBreedOrganism: Coding = {
  code: '131770001',
  display: 'Hebridean sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HeidschnuckeSheepBreedOrganism: Coding = {
  code: '131771002',
  display: 'Heidschnucke sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HerdwickSheepBreedOrganism: Coding = {
  code: '131772009',
  display: 'Herdwick sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HillRadnorSheepBreedOrganism: Coding = {
  code: '131773004',
  display: 'Hill Radnor sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IcelandicSheepBreedOrganism: Coding = {
  code: '131774005',
  display: 'Icelandic sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HarnaiSheepBreedOrganism: Coding = {
  code: '131775006',
  display: 'Harnai sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IstrianPramenkaSheepBreedOrganism: Coding = {
  code: '131776007',
  display: 'Istrian Pramenka sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JacobSheepBreedOrganism: Coding = {
  code: '131777003',
  display: 'Jacob sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JezerskosolcavskaSheepBreedOrganism: Coding = {
  code: '131778008',
  display: 'Jezerskosolcavska sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KachhiSheepBreedOrganism: Coding = {
  code: '131779000',
  display: 'Kachhi sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WensleydaleSheepBreedOrganism: Coding = {
  code: '131780002',
  display: 'Wensleydale sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WestAfricanDwarfSheepBreedOrganism: Coding = {
  code: '131781003',
  display: 'West African Dwarf sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WhiteSuffolkSheepBreedOrganism: Coding = {
  code: '131782005',
  display: 'White Suffolk sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WhitefaceDartmoorSheepBreedOrganism: Coding = {
  code: '131783000',
  display: 'Whiteface Dartmoor sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WhitefaceWoodlandSheepBreedOrganism: Coding = {
  code: '131784006',
  display: 'Whiteface Woodland sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_XinjiangFinewoolSheepBreedOrganism: Coding = {
  code: '131785007',
  display: 'Xinjiang Finewool sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KajliSheepBreedOrganism: Coding = {
  code: '131786008',
  display: 'Kajli sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HogIslandSheepBreedOrganism: Coding = {
  code: '131787004',
  display: 'Hog Island Sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BielleseSheepBreedOrganism: Coding = {
  code: '131788009',
  display: 'Biellese sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChiosSheepBreedOrganism: Coding = {
  code: '131789001',
  display: 'Chios sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SantaCruzSheepBreedOrganism: Coding = {
  code: '131790005',
  display: 'Santa Cruz sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CharollaisSheepBreedOrganism: Coding = {
  code: '131791009',
  display: 'Charollais sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CastlemilkMooritSheepBreedOrganism: Coding = {
  code: '131792002',
  display: 'Castlemilk Moorit sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CampanianBarbarySheepBreedOrganism: Coding = {
  code: '131793007',
  display: 'Campanian Barbary sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CaliforniaVariegatedMutantSheepBreedOrganism: Coding = {
  code: '131794001',
  display: 'California Variegated Mutant sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CaliforniaRedSheepBreedOrganism: Coding = {
  code: '131795000',
  display: 'California Red sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SopravissanaSheepBreedOrganism: Coding = {
  code: '131796004',
  display: 'Sopravissana sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SomaliSheepBreedOrganism: Coding = {
  code: '131797008',
  display: 'Somali sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WelshHillSpeckledFaceSheepBreedOrganism: Coding = {
  code: '131798003',
  display: 'Welsh Hill Speckled Face sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SkuddeSheepBreedOrganism: Coding = {
  code: '131799006',
  display: 'Skudde sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WaziriSheepBreedOrganism: Coding = {
  code: '131800005',
  display: 'Waziri sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShetlandSheepBreedOrganism: Coding = {
  code: '131801009',
  display: 'Shetland sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CambridgeSheepBreedOrganism: Coding = {
  code: '131802002',
  display: 'Cambridge sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SolognoteSheepBreedOrganism: Coding = {
  code: '131803007',
  display: 'Solognote sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ColombianCriolloHorseBreedOrganism: Coding = {
  code: '131804001',
  display: 'Colombian Criollo horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ComtoisHorseBreedOrganism: Coding = {
  code: '131805000',
  display: 'Comtois horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CorsicanHorseBreedOrganism: Coding = {
  code: '131806004',
  display: 'Corsican horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CostaRicanSaddleHorseHorseBreedOrganism: Coding = {
  code: '131807008',
  display: 'Costa Rican Saddle Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CostenoHorseBreedOrganism: Coding = {
  code: '131808003',
  display: 'Costeno horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CubanPasoHorseBreedOrganism: Coding = {
  code: '131809006',
  display: 'Cuban Paso horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CayugaBreedDuckOrganism: Coding = {
  code: '131810001',
  display: 'Cayuga breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RunnerBreedDuckOrganism: Coding = {
  code: '131811002',
  display: 'Runner breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CallBreedDuckOrganism: Coding = {
  code: '131812009',
  display: 'Call breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OrpingtonBreedDuckOrganism: Coding = {
  code: '131813004',
  display: 'Orpington breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CrestedBreedDuckOrganism: Coding = {
  code: '131814005',
  display: 'Crested breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PommeranianBreedDuckOrganism: Coding = {
  code: '131815006',
  display: 'Pommeranian breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RoughFellSheepBreedOrganism: Coding = {
  code: '131816007',
  display: 'Rough Fell sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlueSwedishBreedDuckOrganism: Coding = {
  code: '131817003',
  display: 'Blue Swedish breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Golden300HybridBreedDuckOrganism: Coding = {
  code: '131818008',
  display: 'Golden 300 hybrid breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanishWarmbloodHorseBreedOrganism: Coding = {
  code: '131819000',
  display: 'Danish Warmblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AnconaBreedDuckOrganism: Coding = {
  code: '131820006',
  display: 'Ancona breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustralianSpottedBreedDuckOrganism: Coding = {
  code: '131821005',
  display: 'Australian Spotted breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwaledaleSheepBreedOrganism: Coding = {
  code: '131822003',
  display: 'Swaledale sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolypaySheepBreedOrganism: Coding = {
  code: '131823008',
  display: 'Polypay sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AylesburyBreedDuckOrganism: Coding = {
  code: '131824002',
  display: 'Aylesbury breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackEastIndiesBreedDuckOrganism: Coding = {
  code: '131825001',
  display: 'Black East Indies breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CoastBreedDuckOrganism: Coding = {
  code: '131826000',
  display: 'Coast breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KromsnaveleendBreedDuckOrganism: Coding = {
  code: '131827009',
  display: 'Kromsnaveleend breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MagpieBreedDuckOrganism: Coding = {
  code: '131828004',
  display: 'Magpie breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SilverAppleyardBreedDuckOrganism: Coding = {
  code: '131829007',
  display: 'Silver appleyard breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PagliarolaSheepBreedOrganism: Coding = {
  code: '131830002',
  display: 'Pagliarola sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PomeranianCoarsewoolSheepBreedOrganism: Coding = {
  code: '131831003',
  display: 'Pomeranian Coarsewool sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SheepBreedUndeterminedSheepBreedOrganism: Coding = {
  code: '131832005',
  display: 'Sheep, Breed Undetermined sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OrkneySheepBreedOrganism: Coding = {
  code: '131833000',
  display: 'Orkney sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OldNorwegianSheepBreedOrganism: Coding = {
  code: '131834006',
  display: 'Old Norwegian sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OldFormatSheepBreedOrganism: Coding = {
  code: '131835007',
  display: 'Old Format Sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorwegianFurSheepBreedOrganism: Coding = {
  code: '131836008',
  display: 'Norwegian Fur sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorfolkHornSheepBreedOrganism: Coding = {
  code: '131837004',
  display: 'Norfolk Horn sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NavajoChurroSheepBreedOrganism: Coding = {
  code: '131838009',
  display: 'Navajo-Churro sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwedishYellowBreedDuckOrganism: Coding = {
  code: '131839001',
  display: 'Swedish yellow breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DiepholzGooseBreedOrganism: Coding = {
  code: '131840004',
  display: 'Diepholz goose breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AfricanGooseBreedOrganism: Coding = {
  code: '131841000',
  display: 'African goose breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EmbdenGooseBreedOrganism: Coding = {
  code: '131843002',
  display: 'Embden goose breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChineseWhiteGooseBreedOrganism: Coding = {
  code: '131844008',
  display: 'Chinese white goose breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChineseBrownGooseBreedOrganism: Coding = {
  code: '131845009',
  display: 'Chinese brown goose breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ToulouseGooseBreedOrganism: Coding = {
  code: '131846005',
  display: 'Toulouse goose breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanBuffGooseBreedOrganism: Coding = {
  code: '131847001',
  display: 'American buff goose breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PomeranianGooseBreedOrganism: Coding = {
  code: '131848006',
  display: 'Pomeranian goose breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomanGooseBreedOrganism: Coding = {
  code: '131849003',
  display: 'Roman goose breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ScaniaGooseBreedOrganism: Coding = {
  code: '131850003',
  display: 'Scania goose breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RackaSheepBreedOrganism: Coding = {
  code: '131851004',
  display: 'Racka sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RasaAragonesaSheepBreedOrganism: Coding = {
  code: '131852006',
  display: 'Rasa Aragonesa sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedEngadineSheepBreedOrganism: Coding = {
  code: '131853001',
  display: 'Red Engadine sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RhoenschafSheepBreedOrganism: Coding = {
  code: '131854007',
  display: 'Rhoenschaf sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SebastopolGooseBreedOrganism: Coding = {
  code: '131855008',
  display: 'Sebastopol goose breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwedishIslandGooseBreedOrganism: Coding = {
  code: '131856009',
  display: 'Swedish Island goose breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BronzeTurkeyBreedOrganism: Coding = {
  code: '131857000',
  display: 'Bronze turkey breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AuburnTurkeyBreedOrganism: Coding = {
  code: '131858005',
  display: 'Auburn turkey breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BourbonRedTurkeyBreedOrganism: Coding = {
  code: '131859002',
  display: 'Bourbon Red turkey breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BuffTurkeyBreedOrganism: Coding = {
  code: '131860007',
  display: 'Buff turkey breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HuculHorseBreedOrganism: Coding = {
  code: '131861006',
  display: 'Hucul horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AraAppaloosaHorseBreedOrganism: Coding = {
  code: '131862004',
  display: 'AraAppaloosa horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArgentineCriolloHorseBreedOrganism: Coding = {
  code: '131863009',
  display: 'Argentine Criollo horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArgentinePoloPonyHorseBreedOrganism: Coding = {
  code: '131864003',
  display: 'Argentine Polo Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustralianPonyHorseBreedOrganism: Coding = {
  code: '131865002',
  display: 'Australian Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AuxoisHorseBreedOrganism: Coding = {
  code: '131866001',
  display: 'Auxois horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AveligneseHorseBreedOrganism: Coding = {
  code: '131867005',
  display: 'Avelignese horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AzerbaijanHorseBreedOrganism: Coding = {
  code: '131868000',
  display: 'Azerbaijan horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AzoresHorseBreedOrganism: Coding = {
  code: '131869008',
  display: 'Azores horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BaliHorseBreedOrganism: Coding = {
  code: '131870009',
  display: 'Bali horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BalikunHorseBreedOrganism: Coding = {
  code: '131871008',
  display: 'Balikun horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WaziriHorseBreedOrganism: Coding = {
  code: '131872001',
  display: 'Waziri horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BankerHorseHorseBreedOrganism: Coding = {
  code: '131873006',
  display: 'Banker Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BardigianoHorseBreedOrganism: Coding = {
  code: '131874000',
  display: 'Bardigiano horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BatakHorseBreedOrganism: Coding = {
  code: '131875004',
  display: 'Batak horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BavarianWarmbloodHorseBreedOrganism: Coding = {
  code: '131876003',
  display: 'Bavarian Warmblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelgianArdennaisHorseBreedOrganism: Coding = {
  code: '131877007',
  display: 'Belgian Ardennais horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelgianHalfbloodHorseBreedOrganism: Coding = {
  code: '131878002',
  display: 'Belgian Halfblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelgianWarmbloodHorseBreedOrganism: Coding = {
  code: '131879005',
  display: 'Belgian Warmblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BhutiaHorseBreedOrganism: Coding = {
  code: '131880008',
  display: 'Bhutia horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackSeaHorseHorseBreedOrganism: Coding = {
  code: '131881007',
  display: 'Black Sea Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BosnianHorseBreedOrganism: Coding = {
  code: '131882000',
  display: 'Bosnian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoulonnaisHorseBreedOrganism: Coding = {
  code: '131883005',
  display: 'Boulonnais horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrandenburgHorseBreedOrganism: Coding = {
  code: '131884004',
  display: 'Brandenburg horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrazilianSportHorseHorseBreedOrganism: Coding = {
  code: '131885003',
  display: 'Brazilian Sport Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BritishAppaloosaHorseBreedOrganism: Coding = {
  code: '131886002',
  display: 'British Appaloosa horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BritishRidingPonyHorseBreedOrganism: Coding = {
  code: '131887006',
  display: 'British Riding Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BritishSpottedPonyHorseBreedOrganism: Coding = {
  code: '131888001',
  display: 'British Spotted Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BuohaiHorseBreedOrganism: Coding = {
  code: '131889009',
  display: 'Buohai horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BuryatHorseBreedOrganism: Coding = {
  code: '131890000',
  display: 'Buryat horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CalabrianHorseBreedOrganism: Coding = {
  code: '131891001',
  display: 'Calabrian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CamargueHorseBreedOrganism: Coding = {
  code: '131892008',
  display: 'Camargue horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanadianCuttingHorseHorseBreedOrganism: Coding = {
  code: '131893003',
  display: 'Canadian Cutting Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanadianRusticPonyHorseBreedOrganism: Coding = {
  code: '131894009',
  display: 'Canadian Rustic Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanadianSportHorseHorseBreedOrganism: Coding = {
  code: '131895005',
  display: 'Canadian Sport Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanikHorseBreedOrganism: Coding = {
  code: '131896006',
  display: 'Canik horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CapeHorseHorseBreedOrganism: Coding = {
  code: '131897002',
  display: 'Cape Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CerbatHorseBreedOrganism: Coding = {
  code: '131898007',
  display: 'Cerbat horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChakouyiHorseBreedOrganism: Coding = {
  code: '131899004',
  display: 'Chakouyi horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CharaHorseHorseBreedOrganism: Coding = {
  code: '131900009',
  display: 'Chara Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChickasawHorseBreedOrganism: Coding = {
  code: '131901008',
  display: 'Chickasaw horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChiloteHorseBreedOrganism: Coding = {
  code: '131902001',
  display: 'Chilote horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChineseKazakhHorseBreedOrganism: Coding = {
  code: '131903006',
  display: 'Chinese Kazakh horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChineseMongolianHorseBreedOrganism: Coding = {
  code: '131904000',
  display: 'Chinese Mongolian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChumbivilcasHorseBreedOrganism: Coding = {
  code: '131905004',
  display: 'Chumbivilcas horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChumyshHorseBreedOrganism: Coding = {
  code: '131906003',
  display: 'Chumysh horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CiritHorseBreedOrganism: Coding = {
  code: '131907007',
  display: 'Cirit horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IrishDraftHorseBreedOrganism: Coding = {
  code: '131908002',
  display: 'Irish Draft horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IrishHunterHorseBreedOrganism: Coding = {
  code: '131909005',
  display: 'Irish Hunter horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CubanTrotterHorseBreedOrganism: Coding = {
  code: '131910000',
  display: 'Cuban Trotter horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ItalianHeavyDraftHorseBreedOrganism: Coding = {
  code: '131911001',
  display: 'Italian Heavy Draft horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JabeHorseBreedOrganism: Coding = {
  code: '131912008',
  display: 'Jabe horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JavaHorseBreedOrganism: Coding = {
  code: '131913003',
  display: 'Java horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VendenSheepBreedOrganism: Coding = {
  code: '131914009',
  display: 'Vendéen sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CzechWarmbloodHorseBreedOrganism: Coding = {
  code: '131915005',
  display: 'Czech Warmblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JinhongHorseBreedOrganism: Coding = {
  code: '131916006',
  display: 'Jinhong horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JinzhouHorseBreedOrganism: Coding = {
  code: '131917002',
  display: 'Jinzhou horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SaxonyBreedDuckOrganism: Coding = {
  code: '131918007',
  display: 'Saxony breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanubianHorseBreedOrganism: Coding = {
  code: '131919004',
  display: 'Danubian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KarachaiHorseBreedOrganism: Coding = {
  code: '131920005',
  display: 'Karachai horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KarakacanHorseBreedOrganism: Coding = {
  code: '131921009',
  display: 'Karakacan horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KathiawariHorseBreedOrganism: Coding = {
  code: '131922002',
  display: 'Kathiawari horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KeErQinHorseBreedOrganism: Coding = {
  code: '131923007',
  display: 'Ke-Er-Qin horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KirgizHorseBreedOrganism: Coding = {
  code: '131924001',
  display: 'Kirgiz horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KuznetHorseBreedOrganism: Coding = {
  code: '131925000',
  display: 'Kuznet horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LandaisHorseBreedOrganism: Coding = {
  code: '131926004',
  display: 'Landais horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LewitzerHorseBreedOrganism: Coding = {
  code: '131927008',
  display: 'Lewitzer horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LichuanHorseBreedOrganism: Coding = {
  code: '131928003',
  display: 'Lichuan horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LijiangHorseBreedOrganism: Coding = {
  code: '131929006',
  display: 'Lijiang horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LlaneroHorseBreedOrganism: Coding = {
  code: '131930001',
  display: 'Llanero horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LombokHorseBreedOrganism: Coding = {
  code: '131931002',
  display: 'Lombok horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LundyPonyHorseBreedOrganism: Coding = {
  code: '131932009',
  display: 'Lundy Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MalakanHorseBreedOrganism: Coding = {
  code: '131933004',
  display: 'Malakan horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MalopolskiHorseBreedOrganism: Coding = {
  code: '131934005',
  display: 'Malopolski horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DatongHorseBreedOrganism: Coding = {
  code: '131935006',
  display: 'Datong horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MangalargaPaulistaHorseBreedOrganism: Coding = {
  code: '131936007',
  display: 'Mangalarga Paulista horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DulmenPonyHorseBreedOrganism: Coding = {
  code: '131937003',
  display: 'Dulmen Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MaremmanaHorseBreedOrganism: Coding = {
  code: '131938008',
  display: 'Maremmana horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MarwariHorseBreedOrganism: Coding = {
  code: '131939000',
  display: 'Marwari horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MegezhHorseBreedOrganism: Coding = {
  code: '131940003',
  display: 'Megezh horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MegrelHorseBreedOrganism: Coding = {
  code: '131941004',
  display: 'Megrel horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MerensHorseBreedOrganism: Coding = {
  code: '131942006',
  display: 'Merens horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MessaraHorseBreedOrganism: Coding = {
  code: '131943001',
  display: 'Messara horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SumbaHorseBreedOrganism: Coding = {
  code: '131944007',
  display: 'Sumba horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SumbawaHorseBreedOrganism: Coding = {
  code: '131945008',
  display: 'Sumbawa horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwedishArdennesHorseBreedOrganism: Coding = {
  code: '131946009',
  display: 'Swedish Ardennes horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DutchTuigpaardHorseBreedOrganism: Coding = {
  code: '131947000',
  display: 'Dutch Tuigpaard horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EastAndSoutheastAnadoluHorseBreedOrganism: Coding = {
  code: '131948005',
  display: 'East and Southeast Anadolu horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThaiPonyHorseBreedOrganism: Coding = {
  code: '131949002',
  display: 'Thai Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThessalonianHorseBreedOrganism: Coding = {
  code: '131950002',
  display: 'Thessalonian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TibetanHorseBreedOrganism: Coding = {
  code: '131951003',
  display: 'Tibetan horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TielingHorseBreedOrganism: Coding = {
  code: '131952005',
  display: 'Tieling horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TimorHorseBreedOrganism: Coding = {
  code: '131953000',
  display: 'Timor horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TrakyaHorseBreedOrganism: Coding = {
  code: '131954006',
  display: 'Trakya horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TroteEnGallopeHorseBreedOrganism: Coding = {
  code: '131955007',
  display: 'Trote en Gallope horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TurkomanHorseBreedOrganism: Coding = {
  code: '131956008',
  display: 'Turkoman horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TushinHorseBreedOrganism: Coding = {
  code: '131957004',
  display: 'Tushin horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TuvaHorseBreedOrganism: Coding = {
  code: '131958009',
  display: 'Tuva horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_UzunyaylaHorseBreedOrganism: Coding = {
  code: '131959001',
  display: 'Uzunyayla horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VoronezhCoachHorseHorseBreedOrganism: Coding = {
  code: '131960006',
  display: 'Voronezh Coach Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ElegantWarmbloodHorseBreedOrganism: Coding = {
  code: '131961005',
  display: 'Elegant Warmblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WelshCobHorseBreedOrganism: Coding = {
  code: '131962003',
  display: 'Welsh Cob horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WelshMountainPonyHorseBreedOrganism: Coding = {
  code: '131963008',
  display: 'Welsh Mountain Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EnglishHackHorseBreedOrganism: Coding = {
  code: '131964002',
  display: 'English Hack horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WurttembergHorseBreedOrganism: Coding = {
  code: '131965001',
  display: 'Wurttemberg horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_XilingolHorseBreedOrganism: Coding = {
  code: '131966000',
  display: 'Xilingol horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YanqiHorseBreedOrganism: Coding = {
  code: '131967009',
  display: 'Yanqi horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YemeniHorsesHorseBreedOrganism: Coding = {
  code: '131968004',
  display: 'Yemeni Horses horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YiliHorseBreedOrganism: Coding = {
  code: '131969007',
  display: 'Yili horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YiwuHorseBreedOrganism: Coding = {
  code: '131970008',
  display: 'Yiwu horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YunnanHorseBreedOrganism: Coding = {
  code: '131971007',
  display: 'Yunnan horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanRidingPonyHorseBreedOrganism: Coding = {
  code: '131972000',
  display: 'German Riding Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuanzhongHorseBreedOrganism: Coding = {
  code: '131973005',
  display: 'Guanzhong horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuizhouHorseBreedOrganism: Coding = {
  code: '131974004',
  display: 'Guizhou horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuoxiaHorseBreedOrganism: Coding = {
  code: '131975003',
  display: 'Guoxia horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ErlunchunHorseBreedOrganism: Coding = {
  code: '131976002',
  display: 'Erlunchun horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HalfSaddlebredHorseBreedOrganism: Coding = {
  code: '131977006',
  display: 'Half Saddlebred horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FloresHorseBreedOrganism: Coding = {
  code: '131978001',
  display: 'Flores horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FreibergHorseBreedOrganism: Coding = {
  code: '131979009',
  display: 'Freiberg horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HessenHorseBreedOrganism: Coding = {
  code: '131980007',
  display: 'Hessen horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HinisHorseBreedOrganism: Coding = {
  code: '131981006',
  display: 'Hinis horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HirzaiHorseBreedOrganism: Coding = {
  code: '131982004',
  display: 'Hirzai horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HungarianColdbloodHorseBreedOrganism: Coding = {
  code: '131983009',
  display: 'Hungarian Coldblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HungarianDunHorseBreedOrganism: Coding = {
  code: '131984003',
  display: 'Hungarian Dun horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HungarianSportHorseHorseBreedOrganism: Coding = {
  code: '131985002',
  display: 'Hungarian Sport Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_InternationalStripedHorseHorseBreedOrganism: Coding = {
  code: '131986001',
  display: 'International Striped Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IrishCobHorseBreedOrganism: Coding = {
  code: '131987005',
  display: 'Irish Cob horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MezenHorseBreedOrganism: Coding = {
  code: '131988000',
  display: 'Mezen horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MezohegyesSportHorseHorseBreedOrganism: Coding = {
  code: '131989008',
  display: 'Mezohegyes Sport Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FrenchCobHorseBreedOrganism: Coding = {
  code: '131990004',
  display: 'French Cob horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FrenchSaddlePonyHorseBreedOrganism: Coding = {
  code: '131991000',
  display: 'French Saddle pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MurakozHorseBreedOrganism: Coding = {
  code: '131992007',
  display: 'Murakoz horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FinnhorseDraftHorseBreedOrganism: Coding = {
  code: '131993002',
  display: 'Finnhorse Draft horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MecklenburgHorseBreedOrganism: Coding = {
  code: '131994008',
  display: 'Mecklenburg horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BaheijChickenBreedOrganism: Coding = {
  code: '131995009',
  display: 'Baheij chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AseelChickenBreedOrganism: Coding = {
  code: '131996005',
  display: 'Aseel chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BandaraChickenBreedOrganism: Coding = {
  code: '131997001',
  display: 'Bandara chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CatalanaChickenBreedOrganism: Coding = {
  code: '131998006',
  display: 'Catalana chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustralianGamefowlChickenBreedOrganism: Coding = {
  code: '131999003',
  display: 'Australian Gamefowl chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AyamCemaniChickenBreedOrganism: Coding = {
  code: '132000009',
  display: 'Ayam Cemani chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwedishSpottedHenChickenBreedOrganism: Coding = {
  code: '132001008',
  display: 'Swedish Spotted Hen chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelgianBootedBantamChickenBreedOrganism: Coding = {
  code: '132002001',
  display: 'Belgian Booted Bantam chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrabanterChickenBreedOrganism: Coding = {
  code: '132003006',
  display: 'Brabanter chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BraekelChickenBreedOrganism: Coding = {
  code: '132004000',
  display: 'Braekel chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CochinStandardChickenBreedOrganism: Coding = {
  code: '132005004',
  display: 'Cochin, Standard chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OldEnglishBantamGameChickenBreedOrganism: Coding = {
  code: '132006003',
  display: 'Old English Bantam Game chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GotlandChickenBreedOrganism: Coding = {
  code: '132007007',
  display: 'Gotland chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GroningerMeeuwenChickenBreedOrganism: Coding = {
  code: '132008002',
  display: 'Groninger Meeuwen chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HaitiCrolePigBreedOrganism: Coding = {
  code: '132009005',
  display: 'Haiti Créole pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ManorHybridPigBreedOrganism: Coding = {
  code: '132010000',
  display: 'Manor Hybrid pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HamlinePigBreedOrganism: Coding = {
  code: '132011001',
  display: 'Hamline pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ManorRangerPigBreedOrganism: Coding = {
  code: '132012008',
  display: 'Manor Ranger pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ManorMeishanPigBreedOrganism: Coding = {
  code: '132013003',
  display: 'Manor Meishan pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CotswoldGoldPigBreedOrganism: Coding = {
  code: '132014009',
  display: 'Cotswold Gold pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CotswoldPlatinumPigBreedOrganism: Coding = {
  code: '132015005',
  display: 'Cotswold Platinum pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Cotswold16PigBreedOrganism: Coding = {
  code: '132016006',
  display: 'Cotswold 16 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Cotswold29PigBreedOrganism: Coding = {
  code: '132017002',
  display: 'Cotswold 29 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Cotswold90PigBreedOrganism: Coding = {
  code: '132018007',
  display: 'Cotswold 90 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HampenPigBreedOrganism: Coding = {
  code: '132019004',
  display: 'Hampen pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SPMPigBreedOrganism: Coding = {
  code: '132020005',
  display: 'SPM pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HighConformationWhitePigBreedOrganism: Coding = {
  code: '132021009',
  display: 'High Conformation White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Line32PigBreedOrganism: Coding = {
  code: '132022002',
  display: 'Line 32 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Line21PigBreedOrganism: Coding = {
  code: '132023007',
  display: 'Line 21 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MeatlinePigBreedOrganism: Coding = {
  code: '132024001',
  display: 'Meatline pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HamplinePigBreedOrganism: Coding = {
  code: '132025000',
  display: 'Hampline pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EurolinePigBreedOrganism: Coding = {
  code: '132026004',
  display: 'Euroline pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorlinePigBreedOrganism: Coding = {
  code: '132027008',
  display: 'Norline pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PremierPigBreedOrganism: Coding = {
  code: '132028003',
  display: 'Premier pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TribredPigBreedOrganism: Coding = {
  code: '132029006',
  display: 'Tribred pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanEssexPigBreedOrganism: Coding = {
  code: '132030001',
  display: 'American Essex pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SinoGasconyPigBreedOrganism: Coding = {
  code: '132031002',
  display: 'Sino-Gascony pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuadeloupeCrolePigBreedOrganism: Coding = {
  code: '132032009',
  display: 'Guadeloupe Créole pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ManagraPigBreedOrganism: Coding = {
  code: '132033004',
  display: 'Managra pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanadianLandracePigBreedOrganism: Coding = {
  code: '132034005',
  display: 'Canadian Landrace pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanadianYorkshirePigBreedOrganism: Coding = {
  code: '132035006',
  display: 'Canadian Yorkshire pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PineywoodsPigBreedOrganism: Coding = {
  code: '132037003',
  display: 'Pineywoods pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CatalinaIslandPigBreedOrganism: Coding = {
  code: '132038008',
  display: 'Catalina Island pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RasNLansaPigBreedOrganism: Coding = {
  code: '132039000',
  display: 'Ras-n-Lansa pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PitmanMooreMiniaturePigBreedOrganism: Coding = {
  code: '132040003',
  display: 'Pitman-Moore Miniature pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VitaVetLabMinipigPigBreedOrganism: Coding = {
  code: '132041004',
  display: 'Vita Vet Lab Minipig pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HanfordMiniaturePigBreedOrganism: Coding = {
  code: '132042006',
  display: 'Hanford Miniature pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackHampshirePigBreedOrganism: Coding = {
  code: '132043001',
  display: 'Black Hampshire pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedHampracePigBreedOrganism: Coding = {
  code: '132044007',
  display: 'Red Hamprace pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanYorkshirePigBreedOrganism: Coding = {
  code: '132045008',
  display: 'American Yorkshire pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanBerkshirePigBreedOrganism: Coding = {
  code: '132046009',
  display: 'American Berkshire pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CamboroughBluePigBreedOrganism: Coding = {
  code: '132047000',
  display: 'Camborough Blue pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Camborough12PigBreedOrganism: Coding = {
  code: '132048005',
  display: 'Camborough 12 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WestrainPigBreedOrganism: Coding = {
  code: '132049002',
  display: 'Westrain pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Dalland030PigBreedOrganism: Coding = {
  code: '132050002',
  display: 'Dalland 030 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RazorBackPigBreedOrganism: Coding = {
  code: '132051003',
  display: 'Razor-Back pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MacauPigBreedOrganism: Coding = {
  code: '132052005',
  display: 'Macau pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MouraPigBreedOrganism: Coding = {
  code: '132053000',
  display: 'Moura pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanastraPigBreedOrganism: Coding = {
  code: '132054006',
  display: 'Canastra pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PirapetingaPigBreedOrganism: Coding = {
  code: '132055007',
  display: 'Pirapetinga pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PiauPigBreedOrganism: Coding = {
  code: '132056008',
  display: 'Piau pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NiloCanastraPigBreedOrganism: Coding = {
  code: '132057004',
  display: 'Nilo-Canastra pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanastroPigBreedOrganism: Coding = {
  code: '132058009',
  display: 'Canastrão pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanastroJunqueiraPigBreedOrganism: Coding = {
  code: '132059001',
  display: 'Canastrão, Junqueira pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanastroCapitoChicoPigBreedOrganism: Coding = {
  code: '132060006',
  display: 'Canastrão, Capitão Chico pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanastroZabumbaPigBreedOrganism: Coding = {
  code: '132061005',
  display: 'Canastrão, Zabumba pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanastroCabanoPigBreedOrganism: Coding = {
  code: '132062003',
  display: 'Canastrão, Cabano pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanastroVermelhoPigBreedOrganism: Coding = {
  code: '132063008',
  display: 'Canastrão, Vermelho pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PiauCarunchoPiauPigBreedOrganism: Coding = {
  code: '132064002',
  display: 'Piau, Caruncho Piau pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanastrinhoPigBreedOrganism: Coding = {
  code: '132065001',
  display: 'Canastrinho pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HondurasSwitchTailPigBreedOrganism: Coding = {
  code: '132066000',
  display: 'Honduras Switch-Tail pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MastergiltPigBreedOrganism: Coding = {
  code: '132067009',
  display: 'Mastergilt pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SovereignPigBreedOrganism: Coding = {
  code: '132068004',
  display: 'Sovereign pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PoltavaPigBreedOrganism: Coding = {
  code: '132069007',
  display: 'Poltava pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LipetskPigBreedOrganism: Coding = {
  code: '132070008',
  display: 'Lipetsk pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SovietMeatPigBreedOrganism: Coding = {
  code: '132071007',
  display: 'Soviet Meat pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CentralRussianPigBreedOrganism: Coding = {
  code: '132072000',
  display: 'Central Russian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SteppeMeatPigBreedOrganism: Coding = {
  code: '132073005',
  display: 'Steppe Meat pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KharkovPigBreedOrganism: Coding = {
  code: '132074004',
  display: 'Kharkov pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DnepropetrovskPigBreedOrganism: Coding = {
  code: '132075003',
  display: 'Dnepropetrovsk pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RussianLargeWhitePigBreedOrganism: Coding = {
  code: '132076002',
  display: 'Russian Large White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ForestMountainPigBreedOrganism: Coding = {
  code: '132077006',
  display: 'Forest Mountain pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DnieperPigBreedOrganism: Coding = {
  code: '132078001',
  display: 'Dnieper pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IberianPigBreedOrganism: Coding = {
  code: '132079009',
  display: 'Iberian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IberianExtremaduraRedPigBreedOrganism: Coding = {
  code: '132080007',
  display: 'Iberian, Extremadura Red pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IberianJabugoSpottedPigBreedOrganism: Coding = {
  code: '132081006',
  display: 'Iberian, Jabugo Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IberianBlackIberianPigBreedOrganism: Coding = {
  code: '132082004',
  display: 'Iberian, Black Iberian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PhilippineNativeIlocosPigBreedOrganism: Coding = {
  code: '132083009',
  display: 'Philippine Native, Ilocos pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PhilippineNativeJalajalaPigBreedOrganism: Coding = {
  code: '132084003',
  display: 'Philippine Native, Jalajala pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MangaliztaPigBreedOrganism: Coding = {
  code: '132085002',
  display: 'Mangalizta pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlentejanaPigBreedOrganism: Coding = {
  code: '132086001',
  display: 'Alentejana pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelgianLandraceBNPigBreedOrganism: Coding = {
  code: '132087005',
  display: 'Belgian Landrace, BN pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FrenchLargeWhitePigBreedOrganism: Coding = {
  code: '132088000',
  display: 'French Large White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HyperLargeWhitePigBreedOrganism: Coding = {
  code: '132089008',
  display: 'Hyper Large White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TiaMeslanPigBreedOrganism: Coding = {
  code: '132090004',
  display: 'Tia Meslan pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PenArLan77PigBreedOrganism: Coding = {
  code: '132091000',
  display: 'Pen ar Lan 77 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PenshirePigBreedOrganism: Coding = {
  code: '132092007',
  display: 'Penshire pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LaconiePigBreedOrganism: Coding = {
  code: '132093002',
  display: 'Laconie pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MurcianPigBreedOrganism: Coding = {
  code: '132094008',
  display: 'Murcian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CavallinoPigBreedOrganism: Coding = {
  code: '132095009',
  display: 'Cavallino pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CalabrianPigBreedOrganism: Coding = {
  code: '132096005',
  display: 'Calabrian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ApulianPigBreedOrganism: Coding = {
  code: '132097001',
  display: 'Apulian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SienaBeltedPigBreedOrganism: Coding = {
  code: '132098006',
  display: 'Siena Belted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CalascibettaPigBreedOrganism: Coding = {
  code: '132099003',
  display: 'Calascibetta pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GssingForestPigPigBreedOrganism: Coding = {
  code: '132100006',
  display: 'Güssing Forest Pig pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwissEdelschweinPigBreedOrganism: Coding = {
  code: '132101005',
  display: 'Swiss Edelschwein pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorthCaucasusPigBreedOrganism: Coding = {
  code: '132102003',
  display: 'North Caucasus pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DonPigBreedOrganism: Coding = {
  code: '132103008',
  display: 'Don pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RostovPigBreedOrganism: Coding = {
  code: '132104002',
  display: 'Rostov pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RussianLongEaredWhitePigBreedOrganism: Coding = {
  code: '132105001',
  display: 'Russian Long-Eared White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RussianShortEaredWhitePigBreedOrganism: Coding = {
  code: '132106000',
  display: 'Russian Short-Eared White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PrisheksninskPigBreedOrganism: Coding = {
  code: '132107009',
  display: 'Prisheksninsk pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BreitovPigBreedOrganism: Coding = {
  code: '132108004',
  display: 'Breitov pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LivnyPigBreedOrganism: Coding = {
  code: '132109007',
  display: 'Livny pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TsivilskPigBreedOrganism: Coding = {
  code: '132110002',
  display: 'Tsivilsk pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_UrzhumPigBreedOrganism: Coding = {
  code: '132111003',
  display: 'Urzhum pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MinisibPigBreedOrganism: Coding = {
  code: '132112005',
  display: 'Minisib pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SakhalinWhitePigBreedOrganism: Coding = {
  code: '132113000',
  display: 'Sakhalin White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorthSiberianPigBreedOrganism: Coding = {
  code: '132114006',
  display: 'North Siberian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SiberianBlackPiedPigBreedOrganism: Coding = {
  code: '132115007',
  display: 'Siberian Black Pied pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KemerovoPigBreedOrganism: Coding = {
  code: '132116008',
  display: 'Kemerovo pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KM1PigBreedOrganism: Coding = {
  code: '132117004',
  display: 'KM-1 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AksaBlackPiedPigBreedOrganism: Coding = {
  code: '132118009',
  display: 'Aksaî Black Pied pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SemirechenskPigBreedOrganism: Coding = {
  code: '132119001',
  display: 'Semirechensk pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MinPigBreedOrganism: Coding = {
  code: '132120007',
  display: 'Min pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SanjiangWhitePigBreedOrganism: Coding = {
  code: '132121006',
  display: 'Sanjiang White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BasqueBlackPiedPigBreedOrganism: Coding = {
  code: '132122004',
  display: 'Basque Black Pied pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CorsicanPigBreedOrganism: Coding = {
  code: '132123009',
  display: 'Corsican pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CrolePigBreedOrganism: Coding = {
  code: '132124003',
  display: 'Créole pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GasconyPigBreedOrganism: Coding = {
  code: '132125002',
  display: 'Gascony pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LimousinPigBreedOrganism: Coding = {
  code: '132126001',
  display: 'Limousin pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HarbinWhitePigBreedOrganism: Coding = {
  code: '132127005',
  display: 'Harbin White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HeilongjiangSpottedPigBreedOrganism: Coding = {
  code: '132128000',
  display: 'Heilongjiang Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LiaoningBlackPigBreedOrganism: Coding = {
  code: '132129008',
  display: 'Liaoning Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HuangHuaiHaiBlackShenxianPigBreedOrganism: Coding = {
  code: '132130003',
  display: 'Huang-Huai-Hai Black, Shenxian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HuangHuaiHaiBlackPigBreedOrganism: Coding = {
  code: '132131004',
  display: 'Huang-Huai-Hai Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BameiPigBreedOrganism: Coding = {
  code: '132132006',
  display: 'Bamei pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HanjiangBlackPigBreedOrganism: Coding = {
  code: '132133001',
  display: 'Hanjiang Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DingPigBreedOrganism: Coding = {
  code: '132134007',
  display: 'Ding pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HuaiPigBreedOrganism: Coding = {
  code: '132135008',
  display: 'Huai pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NewHuaiPigBreedOrganism: Coding = {
  code: '132136009',
  display: 'New Huai pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MashenPigBreedOrganism: Coding = {
  code: '132137000',
  display: 'Mashen pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YimengBlackPigBreedOrganism: Coding = {
  code: '132138005',
  display: 'Yimeng Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HetaoLopEarPigBreedOrganism: Coding = {
  code: '132139002',
  display: 'Hetao Lop-Ear pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KoreanNativePigBreedOrganism: Coding = {
  code: '132140000',
  display: 'Korean Native pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KoreanImprovedPigBreedOrganism: Coding = {
  code: '132141001',
  display: 'Korean Improved pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PenbukPigBreedOrganism: Coding = {
  code: '132142008',
  display: 'Penbuk pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BeijingBlackPigBreedOrganism: Coding = {
  code: '132143003',
  display: 'Beijing Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChenghuaPigBreedOrganism: Coding = {
  code: '132144009',
  display: 'Chenghua pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TaoyuanPigBreedOrganism: Coding = {
  code: '132145005',
  display: 'Taoyuan pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TaiwanSmallBlackPigBreedOrganism: Coding = {
  code: '132146006',
  display: 'Taiwan Small Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TaiwanSmallRedPigBreedOrganism: Coding = {
  code: '132147002',
  display: 'Taiwan Small Red pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuanlingPigBreedOrganism: Coding = {
  code: '132148007',
  display: 'Guanling pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HuchuanMountainPigBreedOrganism: Coding = {
  code: '132149004',
  display: 'Huchuan Mountain pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RongchangPigBreedOrganism: Coding = {
  code: '132150004',
  display: 'Rongchang pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WujinPigBreedOrganism: Coding = {
  code: '132151000',
  display: 'Wujin pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DahePigBreedOrganism: Coding = {
  code: '132152007',
  display: 'Dahe pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YananPigBreedOrganism: Coding = {
  code: '132153002',
  display: 'Yanan pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthYunnanShortEaredPigBreedOrganism: Coding = {
  code: '132154008',
  display: 'South Yunnan Short-Eared pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HainanLingaoPigBreedOrganism: Coding = {
  code: '132155009',
  display: 'Hainan, Lingao pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HainanTunchangPigBreedOrganism: Coding = {
  code: '132156005',
  display: 'Hainan, Tunchang pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HainanWenchangPigBreedOrganism: Coding = {
  code: '132157001',
  display: 'Hainan, Wenchang pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LiangGuangSmallSpottedPigBreedOrganism: Coding = {
  code: '132158006',
  display: 'Liang Guang Small Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanPasturePigBreedOrganism: Coding = {
  code: '132159003',
  display: 'German Pasture pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PiauSorocabaPigBreedOrganism: Coding = {
  code: '132160008',
  display: 'Piau, Sorocaba pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NiloPigBreedOrganism: Coding = {
  code: '132161007',
  display: 'Nilo pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BahiaPigBreedOrganism: Coding = {
  code: '132162000',
  display: 'Bahia pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PernaCurtaPigBreedOrganism: Coding = {
  code: '132163005',
  display: 'Perna-Curta pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CarunchinhoPigBreedOrganism: Coding = {
  code: '132164004',
  display: 'Carunchinho pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MandiPigBreedOrganism: Coding = {
  code: '132165003',
  display: 'Mandi pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OrehlaDeColherPigBreedOrganism: Coding = {
  code: '132166002',
  display: 'Orehla de Colher pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VenezuelanBlackPigBreedOrganism: Coding = {
  code: '132167006',
  display: 'Venezuelan Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BolivianPigBreedOrganism: Coding = {
  code: '132168001',
  display: 'Bolivian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PelnPigBreedOrganism: Coding = {
  code: '132169009',
  display: 'Pelón pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MexicanWattledPigBreedOrganism: Coding = {
  code: '132170005',
  display: 'Mexican Wattled pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Dalland080PigBreedOrganism: Coding = {
  code: '132171009',
  display: 'Dalland 080 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MonarchPigBreedOrganism: Coding = {
  code: '132173007',
  display: 'Monarch pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BisaroPigBreedOrganism: Coding = {
  code: '132174001',
  display: 'Bisaro pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackHairlessPigBreedOrganism: Coding = {
  code: '132175000',
  display: 'Black Hairless pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackMangalitsaPigBreedOrganism: Coding = {
  code: '132176004',
  display: 'Black Mangalitsa pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BorghigianaPigBreedOrganism: Coding = {
  code: '132178003',
  display: 'Borghigiana pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChianinaPigBreedOrganism: Coding = {
  code: '132179006',
  display: 'Chianina pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CosentinaPigBreedOrganism: Coding = {
  code: '132180009',
  display: 'Cosentina pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CuinoPigBreedOrganism: Coding = {
  code: '132181008',
  display: 'Cuino pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FriuliBlackPigBreedOrganism: Coding = {
  code: '132182001',
  display: 'Friuli Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FumatiPigBreedOrganism: Coding = {
  code: '132183006',
  display: 'Fumati pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GalicianPigBreedOrganism: Coding = {
  code: '132184000',
  display: 'Galician pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanBerkshirePigBreedOrganism: Coding = {
  code: '132185004',
  display: 'German Berkshire pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GhoriPigBreedOrganism: Coding = {
  code: '132186003',
  display: 'Ghori pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JianliPigBreedOrganism: Coding = {
  code: '132187007',
  display: 'Jianli pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LucanianPigBreedOrganism: Coding = {
  code: '132188002',
  display: 'Lucanian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MaremmanaPigBreedOrganism: Coding = {
  code: '132189005',
  display: 'Maremmana pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MiamiPigBreedOrganism: Coding = {
  code: '132190001',
  display: 'Miami pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MontmorillonPigBreedOrganism: Coding = {
  code: '132191002',
  display: 'Montmorillon pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OldSwedishSpottedPigBreedOrganism: Coding = {
  code: '132192009',
  display: 'Old Swedish Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OliventinaPigBreedOrganism: Coding = {
  code: '132193004',
  display: 'Oliventina pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ParmensePigBreedOrganism: Coding = {
  code: '132194005',
  display: 'Parmense pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomagnolaPigBreedOrganism: Coding = {
  code: '132195006',
  display: 'Romagnola pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SiberianPigBreedOrganism: Coding = {
  code: '132196007',
  display: 'Siberian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SmallWhitePigBreedOrganism: Coding = {
  code: '132197003',
  display: 'Small White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BaltaretPigBreedOrganism: Coding = {
  code: '132198008',
  display: 'Baltaret pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TunchangPigBreedOrganism: Coding = {
  code: '132199000',
  display: 'Tunchang pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SterlingPigBreedOrganism: Coding = {
  code: '132200002',
  display: 'Sterling pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VichPigBreedOrganism: Coding = {
  code: '132201003',
  display: 'Vich pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VietnamesePigBreedOrganism: Coding = {
  code: '132202005',
  display: 'Vietnamese pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VitoriaPigBreedOrganism: Coding = {
  code: '132203000',
  display: 'Vitoria pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WaiChowPigBreedOrganism: Coding = {
  code: '132204006',
  display: 'Wai Chow pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YorkshireBlueAndWhitePigBreedOrganism: Coding = {
  code: '132205007',
  display: 'Yorkshire Blue and White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Dalland020PigBreedOrganism: Coding = {
  code: '132206008',
  display: 'Dalland 020 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WiltshirePigBreedOrganism: Coding = {
  code: '132207004',
  display: 'Wiltshire pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HamrocPigBreedOrganism: Coding = {
  code: '132208009',
  display: 'Hamroc pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DRUTMTerminalsPigBreedOrganism: Coding = {
  code: '132209001',
  display: 'DRU (TM) Terminals pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Camborough22PigBreedOrganism: Coding = {
  code: '132210006',
  display: 'Camborough 22 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Camborough15PigBreedOrganism: Coding = {
  code: '132211005',
  display: 'Camborough 15 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PR1050PigBreedOrganism: Coding = {
  code: '132212003',
  display: 'PR 1050 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PR1075PigBreedOrganism: Coding = {
  code: '132213008',
  display: 'PR 1075 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChryakPICPigBreedOrganism: Coding = {
  code: '132214002',
  display: 'Chryak PIC pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanadianRoyalBluePigBreedOrganism: Coding = {
  code: '132215001',
  display: 'Canadian Royal Blue pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Line500DurocPigBreedOrganism: Coding = {
  code: '132216000',
  display: 'Line 500 Duroc pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Bodmin950PigBreedOrganism: Coding = {
  code: '132217009',
  display: 'Bodmin 950 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanadianDurocPigBreedOrganism: Coding = {
  code: '132218004',
  display: 'Canadian Duroc pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanadianHampshirePigBreedOrganism: Coding = {
  code: '132219007',
  display: 'Canadian Hampshire pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BaXuyenPigBreedOrganism: Coding = {
  code: '132220001',
  display: 'Ba Xuyen pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArapawaIslandPigBreedOrganism: Coding = {
  code: '132221002',
  display: 'Arapawa Island pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WuzhishanPigBreedOrganism: Coding = {
  code: '132222009',
  display: 'Wuzhishan pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PhilippineNativePigBreedOrganism: Coding = {
  code: '132223004',
  display: 'Philippine Native pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SinclairMiniaturePigBreedOrganism: Coding = {
  code: '132224005',
  display: 'Sinclair Miniature pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SaddlebackPigBreedOrganism: Coding = {
  code: '132225006',
  display: 'Saddleback pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YucatanMiniaturePigBreedOrganism: Coding = {
  code: '132226007',
  display: 'Yucatan Miniature pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BantuPigBreedOrganism: Coding = {
  code: '132227003',
  display: 'Bantu pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TibetanPigBreedOrganism: Coding = {
  code: '132228008',
  display: 'Tibetan pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TuropoljePigBreedOrganism: Coding = {
  code: '132229000',
  display: 'Turopolje pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VietnamesePotBelliedPigPigBreedOrganism: Coding = {
  code: '132230005',
  display: 'Vietnamese Pot-Bellied Pig pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanLandracePigBreedOrganism: Coding = {
  code: '132231009',
  display: 'American Landrace pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwallowBeliedMangalitzaPigBreedOrganism: Coding = {
  code: '132232002',
  display: 'Swallow Belied Mangalitza pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FengjingPigBreedOrganism: Coding = {
  code: '132233007',
  display: 'Fengjing pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FinnishLandracePigBreedOrganism: Coding = {
  code: '132234001',
  display: 'Finnish Landrace pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuineaHogPigBreedOrganism: Coding = {
  code: '132235000',
  display: 'Guinea Hog pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HezuoPigBreedOrganism: Coding = {
  code: '132236004',
  display: 'Hezuo pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OssabawIslandPigBreedOrganism: Coding = {
  code: '132237008',
  display: 'Ossabaw Island pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KelePigBreedOrganism: Coding = {
  code: '132238003',
  display: 'Kele pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KrskopoljePigBreedOrganism: Coding = {
  code: '132239006',
  display: 'Krskopolje pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KunekunePigBreedOrganism: Coding = {
  code: '132240008',
  display: 'Kunekune pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LargeBlackWhitePigBreedOrganism: Coding = {
  code: '132241007',
  display: 'Large Black-White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LithuanianNativePigBreedOrganism: Coding = {
  code: '132242000',
  display: 'Lithuanian Native pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MeishanPigBreedOrganism: Coding = {
  code: '132243005',
  display: 'Meishan pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JinhuaPigBreedOrganism: Coding = {
  code: '132244004',
  display: 'Jinhua pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NingxiangPigBreedOrganism: Coding = {
  code: '132245003',
  display: 'Ningxiang pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MoraRomagnolaPigBreedOrganism: Coding = {
  code: '132246002',
  display: 'Mora Romagnola pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MukotaPigBreedOrganism: Coding = {
  code: '132247006',
  display: 'Mukota pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MinzhuPigBreedOrganism: Coding = {
  code: '132248001',
  display: 'Minzhu pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NeijiangPigBreedOrganism: Coding = {
  code: '132249009',
  display: 'Neijiang pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MulefootPigBreedOrganism: Coding = {
  code: '132250009',
  display: 'Mulefoot pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NormandPigBreedOrganism: Coding = {
  code: '132251008',
  display: 'Normand pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AngelnSaddlebackPigBreedOrganism: Coding = {
  code: '132252001',
  display: 'Angeln Saddleback pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GreekLocalPigBreedOrganism: Coding = {
  code: '132253006',
  display: 'Greek Local pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IcelandicPigBreedOrganism: Coding = {
  code: '132254000',
  display: 'Icelandic pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CasertanaPigBreedOrganism: Coding = {
  code: '132255004',
  display: 'Casertana pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MadonieSicilianPigBreedOrganism: Coding = {
  code: '132256003',
  display: 'Madonie-Sicilian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SardinianPigBreedOrganism: Coding = {
  code: '132257007',
  display: 'Sardinian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SicilianPigBreedOrganism: Coding = {
  code: '132258002',
  display: 'Sicilian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ZlotnikiSpottedPigBreedOrganism: Coding = {
  code: '132259005',
  display: 'Zlotniki Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ZlotnikiWhitePigBreedOrganism: Coding = {
  code: '132260000',
  display: 'Zlotniki White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SiskaPigBreedOrganism: Coding = {
  code: '132261001',
  display: 'Siska pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SumadijaPigBreedOrganism: Coding = {
  code: '132262008',
  display: 'Sumadija pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FroxfieldPygmyPigBreedOrganism: Coding = {
  code: '132263003',
  display: 'Froxfield Pygmy pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanishLargeWhitePigBreedOrganism: Coding = {
  code: '132264009',
  display: 'Danish Large White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanishDurocPigBreedOrganism: Coding = {
  code: '132265005',
  display: 'Danish Duroc pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanishHampshirePigBreedOrganism: Coding = {
  code: '132266006',
  display: 'Danish Hampshire pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PigghamPigBreedOrganism: Coding = {
  code: '132267002',
  display: 'Piggham pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NewYorkRedPigBreedOrganism: Coding = {
  code: '132268007',
  display: 'New York Red pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FinnishYorkshirePigBreedOrganism: Coding = {
  code: '132269004',
  display: 'Finnish Yorkshire pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DutchYorkshirePigBreedOrganism: Coding = {
  code: '132270003',
  display: 'Dutch Yorkshire pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PulawyPigBreedOrganism: Coding = {
  code: '132271004',
  display: 'Pulawy pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PomeranianPigBreedOrganism: Coding = {
  code: '132272006',
  display: 'Pomeranian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolishLandracePigBreedOrganism: Coding = {
  code: '132273001',
  display: 'Polish Landrace pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EstonianBaconPigBreedOrganism: Coding = {
  code: '132274007',
  display: 'Estonian Bacon pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LatvianWhitePigBreedOrganism: Coding = {
  code: '132275008',
  display: 'Latvian White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LithuanianWhitePigBreedOrganism: Coding = {
  code: '132276009',
  display: 'Lithuanian White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BKB1PigBreedOrganism: Coding = {
  code: '132277000',
  display: 'BKB-1 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelorusBlackPiedPigBreedOrganism: Coding = {
  code: '132278005',
  display: 'Belorus Black Pied pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MirgorodPigBreedOrganism: Coding = {
  code: '132279002',
  display: 'Mirgorod pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LiangGuangSmallSpottedLuchuanPigBreedOrganism: Coding = {
  code: '132280004',
  display: 'Liang Guang Small Spotted, Luchuan pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FujianSmallPigBreedOrganism: Coding = {
  code: '132281000',
  display: 'Fujian Small pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorthFujianBlackAndWhitePigBreedOrganism: Coding = {
  code: '132282007',
  display: 'North Fujian Black-and-White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FuanSpottedPigBreedOrganism: Coding = {
  code: '132283002',
  display: 'Fuan Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PutianPigBreedOrganism: Coding = {
  code: '132284008',
  display: 'Putian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FuzhouBlackPigBreedOrganism: Coding = {
  code: '132285009',
  display: 'Fuzhou Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MinbeiSpottedPigBreedOrganism: Coding = {
  code: '132286005',
  display: 'Minbei Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LantangPigBreedOrganism: Coding = {
  code: '132287001',
  display: 'Lantang pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LiangGuangSmallSpottedGuangdongSmallEarPigBreedOrganism: Coding = {
  code: '132288006',
  display: 'Liang Guang Small Spotted, Guangdong Small Ear pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LonglinPigBreedOrganism: Coding = {
  code: '132289003',
  display: 'Longlin pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YuedongBlackPigBreedOrganism: Coding = {
  code: '132290007',
  display: 'Yuedong Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_XiangPigBreedOrganism: Coding = {
  code: '132291006',
  display: 'Xiang pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CantonesePigBreedOrganism: Coding = {
  code: '132292004',
  display: 'Cantonese pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JinhuaDongyangPigBreedOrganism: Coding = {
  code: '132293009',
  display: 'Jinhua, Dongyang pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JinhuaYongkangPigBreedOrganism: Coding = {
  code: '132294003',
  display: 'Jinhua, Yongkang pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DaweiziPigBreedOrganism: Coding = {
  code: '132295002',
  display: 'Daweizi pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HuazhongTwoEndBlackPigBreedOrganism: Coding = {
  code: '132296001',
  display: 'Huazhong Two-End Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HuazhongTwoEndBlackJianliPigBreedOrganism: Coding = {
  code: '132297005',
  display: 'Huazhong Two-End Black, Jianli pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HuazhongTwoEndBlackTongchengPigBreedOrganism: Coding = {
  code: '132298000',
  display: 'Huazhong Two-End Black, Tongcheng pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HuazhongTwoEndBlackSatzelingPigBreedOrganism: Coding = {
  code: '132299008',
  display: 'Huazhong Two-End Black, Satzeling pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GanzhongnanSpottedPigBreedOrganism: Coding = {
  code: '132300000',
  display: 'Ganzhongnan Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HangPigBreedOrganism: Coding = {
  code: '132301001',
  display: 'Hang pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LepingPigBreedOrganism: Coding = {
  code: '132302008',
  display: 'Leping pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LongyouBlackPigBreedOrganism: Coding = {
  code: '132303003',
  display: 'Longyou Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WuyiBlackPigBreedOrganism: Coding = {
  code: '132304009',
  display: 'Wuyi Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LeeSungPigBreedOrganism: Coding = {
  code: '132305005',
  display: 'Lee-Sung pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LanYuPigBreedOrganism: Coding = {
  code: '132306006',
  display: 'Lan-Yu pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VietnameseYorkshirePigBreedOrganism: Coding = {
  code: '132307002',
  display: 'Vietnamese Yorkshire pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YujiangPigBreedOrganism: Coding = {
  code: '132308007',
  display: 'Yujiang pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WanzheSpottedPigBreedOrganism: Coding = {
  code: '132309004',
  display: 'Wanzhe Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WanzheSpottedChunanSpottedPigBreedOrganism: Coding = {
  code: '132310009',
  display: 'Wanzhe Spotted, Chunan Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WanzheSpottedWannanSpottedPigBreedOrganism: Coding = {
  code: '132311008',
  display: 'Wanzhe Spotted, Wannan Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShengxianSpottedPigBreedOrganism: Coding = {
  code: '132312001',
  display: 'Shengxian Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_QingpingPigBreedOrganism: Coding = {
  code: '132313006',
  display: 'Qingping pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_XiangxiBlackPigBreedOrganism: Coding = {
  code: '132314000',
  display: 'Xiangxi Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BamaxiangPigBreedOrganism: Coding = {
  code: '132315004',
  display: 'Bamaxiang pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TaihuPigBreedOrganism: Coding = {
  code: '132316003',
  display: 'Taihu pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ErhulianPigBreedOrganism: Coding = {
  code: '132317007',
  display: 'Erhulian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JiaxingBlackPigBreedOrganism: Coding = {
  code: '132318002',
  display: 'Jiaxing Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MiPigBreedOrganism: Coding = {
  code: '132319005',
  display: 'Mi pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShahutouPigBreedOrganism: Coding = {
  code: '132320004',
  display: 'Shahutou pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JiaoxiPigBreedOrganism: Coding = {
  code: '132321000',
  display: 'Jiaoxi pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShanghaiWhitePigBreedOrganism: Coding = {
  code: '132322007',
  display: 'Shanghai White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HubeiWhitePigBreedOrganism: Coding = {
  code: '132323002',
  display: 'Hubei White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_XinjinPigBreedOrganism: Coding = {
  code: '132324008',
  display: 'Xinjin pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_XinjinJilinBlackPigBreedOrganism: Coding = {
  code: '132325009',
  display: 'Xinjin, Jilin Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_XinjinNingAnPigBreedOrganism: Coding = {
  code: '132326005',
  display: 'Xinjin, Ning-an pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PigBreedOrganism: Coding = {
  code: '132327001',
  display: 'Í pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DBIPigBreedOrganism: Coding = {
  code: '132328006',
  display: 'DBI pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_XinjinXinjinPigBreedOrganism: Coding = {
  code: '132329003',
  display: 'Xinjin, Xinjin pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MeixinPigBreedOrganism: Coding = {
  code: '132330008',
  display: 'Meixin pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorthEastChinaSpottedPigBreedOrganism: Coding = {
  code: '132331007',
  display: 'North East China Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FannongSpottedPigBreedOrganism: Coding = {
  code: '132332000',
  display: 'Fannong Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LaoshanPigBreedOrganism: Coding = {
  code: '132333005',
  display: 'Laoshan pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NanjingBlackPigBreedOrganism: Coding = {
  code: '132334004',
  display: 'Nanjing Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShanxiBlackPigBreedOrganism: Coding = {
  code: '132335003',
  display: 'Shanxi Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GanzhouWhitePigBreedOrganism: Coding = {
  code: '132336002',
  display: 'Ganzhou White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuangxiWhitePigBreedOrganism: Coding = {
  code: '132337006',
  display: 'Guangxi White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HanzhongWhitePigBreedOrganism: Coding = {
  code: '132338001',
  display: 'Hanzhong White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LutaiWhitePigBreedOrganism: Coding = {
  code: '132339009',
  display: 'Lutai White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YiliWhitePigBreedOrganism: Coding = {
  code: '132340006',
  display: 'Yili White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_XinjiangWhitePigBreedOrganism: Coding = {
  code: '132341005',
  display: 'Xinjiang White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BSIPigBreedOrganism: Coding = {
  code: '132342003',
  display: 'BSI pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MongCaiPigBreedOrganism: Coding = {
  code: '132343008',
  display: 'Mong Cai pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LangHongPigBreedOrganism: Coding = {
  code: '132344002',
  display: 'Lang Hong pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MuongKhuongPigBreedOrganism: Coding = {
  code: '132345001',
  display: 'Muong Khuong pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MeoPigBreedOrganism: Coding = {
  code: '132346000',
  display: 'Meo pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TongConPigBreedOrganism: Coding = {
  code: '132347009',
  display: 'Tong Con pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HaBacPigBreedOrganism: Coding = {
  code: '132348004',
  display: 'Ha Bac pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThaiBinhPigBreedOrganism: Coding = {
  code: '132349007',
  display: 'Thai Binh pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CoPigBreedOrganism: Coding = {
  code: '132350007',
  display: 'Co pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwissImprovedLandracePigBreedOrganism: Coding = {
  code: '132351006',
  display: 'Swiss Improved Landrace pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanLandraceBPigBreedOrganism: Coding = {
  code: '132352004',
  display: 'German Landrace B pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EdelschweinPigBreedOrganism: Coding = {
  code: '132353009',
  display: 'Edelschwein pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwabianHallPigBreedOrganism: Coding = {
  code: '132354003',
  display: 'Swabian-Hall pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BentheimBlackPiedPigBreedOrganism: Coding = {
  code: '132355002',
  display: 'Bentheim Black Pied pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BaldingerSpottedPigBreedOrganism: Coding = {
  code: '132356001',
  display: 'Baldinger Spotted pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanRedPiedPigBreedOrganism: Coding = {
  code: '132357005',
  display: 'German Red Pied pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanCornwallPigBreedOrganism: Coding = {
  code: '132358000',
  display: 'German Cornwall pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GttingenMiniaturePigBreedOrganism: Coding = {
  code: '132359008',
  display: 'Göttingen Miniature pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MunichMiniaturePigBreedOrganism: Coding = {
  code: '132360003',
  display: 'Munich Miniature pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LeicomaPigBreedOrganism: Coding = {
  code: '132361004',
  display: 'Leicoma pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SchwerfurtMeatPigBreedOrganism: Coding = {
  code: '132362006',
  display: 'Schwerfurt Meat pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HungarianWhitePigBreedOrganism: Coding = {
  code: '132363001',
  display: 'Hungarian White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HungahybPigBreedOrganism: Coding = {
  code: '132364007',
  display: 'Hungahyb pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BulgarianNativePigBreedOrganism: Coding = {
  code: '132365008',
  display: 'Bulgarian Native pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EastBalkanPigBreedOrganism: Coding = {
  code: '132366009',
  display: 'East Balkan pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KulaPigBreedOrganism: Coding = {
  code: '132367000',
  display: 'Kula pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NghiaBinhPigBreedOrganism: Coding = {
  code: '132368005',
  display: 'Nghia Binh pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BergamascaSheepBreedOrganism: Coding = {
  code: '132681007',
  display: 'Bergamasca sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PortlandSheepBreedOrganism: Coding = {
  code: '132682000',
  display: 'Portland sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WeisseHornloseHeidschnuckeSheepBreedOrganism: Coding = {
  code: '132684004',
  display: 'Weisse Hornlose Heidschnucke sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DrentsHeideschaapSheepBreedOrganism: Coding = {
  code: '132685003',
  display: 'Drents Heideschaap sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KameroenSheepBreedOrganism: Coding = {
  code: '132686002',
  display: 'Kameroen sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MergellandSheepBreedOrganism: Coding = {
  code: '132687006',
  display: 'Mergelland sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OuessantSheepBreedOrganism: Coding = {
  code: '132688001',
  display: 'Ouessant sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanadianArcottSheepBreedOrganism: Coding = {
  code: '132689009',
  display: 'Canadian Arcott sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NoordhollanderSheepBreedOrganism: Coding = {
  code: '132690000',
  display: 'Noordhollander sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChocolateTurkeyBreedOrganism: Coding = {
  code: '132691001',
  display: 'Chocolate turkey breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LilacTurkeyBreedOrganism: Coding = {
  code: '132692008',
  display: 'Lilac turkey breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SilverAuburnTurkeyBreedOrganism: Coding = {
  code: '132693003',
  display: 'Silver Auburn turkey breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WildCrossTurkeyOrganism: Coding = {
  code: '132695005',
  display: 'Wild cross turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MatrouhChickenBreedOrganism: Coding = {
  code: '132696006',
  display: 'Matrouh chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RijnlamASheepBreedOrganism: Coding = {
  code: '132697002',
  display: 'Rijnlam-A sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SchoonebekerSheepBreedOrganism: Coding = {
  code: '132698007',
  display: 'Schoonebeker sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WallisBlacknosedSheepBreedOrganism: Coding = {
  code: '132699004',
  display: 'Wallis Blacknosed Sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KhakiCampbellBreedDuckOrganism: Coding = {
  code: '132700003',
  display: 'Khaki Campbell breed duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NewfoundlandSheepBreedOrganism: Coding = {
  code: '132701004',
  display: 'Newfoundland sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WallisCountrySheepBreedOrganism: Coding = {
  code: '132702006',
  display: 'Wallis Country Sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RideauArcottSheepBreedOrganism: Coding = {
  code: '132703001',
  display: 'Rideau Arcott sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TukidaleSheepBreedOrganism: Coding = {
  code: '132704007',
  display: 'Tukidale sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolwarthSheepBreedOrganism: Coding = {
  code: '132705008',
  display: 'Polwarth sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RyelandSheepBreedOrganism: Coding = {
  code: '132706009',
  display: 'Ryeland sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThalliSheepBreedOrganism: Coding = {
  code: '132707000',
  display: 'Thalli sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TongSheepBreedOrganism: Coding = {
  code: '132708005',
  display: 'Tong sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TouabireSheepBreedOrganism: Coding = {
  code: '132709002',
  display: 'Touabire sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TunisSheepBreedOrganism: Coding = {
  code: '132710007',
  display: 'Tunis sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TyrolMountainSheepBreedOrganism: Coding = {
  code: '132711006',
  display: 'Tyrol Mountain sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_UdaSheepBreedOrganism: Coding = {
  code: '132712004',
  display: 'Uda sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GrayChickenBreedOrganism: Coding = {
  code: '132713009',
  display: 'Gray chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HamburgsChickenBreedOrganism: Coding = {
  code: '132714003',
  display: 'Hamburgs chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GimmizahChickenBreedOrganism: Coding = {
  code: '132715002',
  display: 'Gimmizah chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanMuttonMerinoSheepBreedOrganism: Coding = {
  code: '132716001',
  display: 'German Mutton Merino sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MediumWoolMerinoSheepBreedOrganism: Coding = {
  code: '132717005',
  display: 'Medium-Wool Merino sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FonthillMerinoSheepBreedOrganism: Coding = {
  code: '132718000',
  display: 'Fonthill Merino sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthAfricanMuttonMerinoSheepBreedOrganism: Coding = {
  code: '132719008',
  display: 'South African Mutton Merino sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_StrongWoolMerinoSheepBreedOrganism: Coding = {
  code: '132720002',
  display: 'Strong Wool Merino sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PollMerinoSheepBreedOrganism: Coding = {
  code: '132721003',
  display: 'Poll Merino sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FineMerinoSheepBreedOrganism: Coding = {
  code: '132722005',
  display: 'Fine Merino sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthAfricanMerinoSheepBreedOrganism: Coding = {
  code: '132723000',
  display: 'South African Merino sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SuperfineMerinoSheepBreedOrganism: Coding = {
  code: '132724006',
  display: 'Superfine Merino sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DutchBantamChickenBreedOrganism: Coding = {
  code: '132725007',
  display: 'Dutch Bantam chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SilverMontazahChickenBreedOrganism: Coding = {
  code: '132726008',
  display: 'Silver Montazah chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwissHenChickenBreedOrganism: Coding = {
  code: '132727004',
  display: 'Swiss Hen chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_StyrianChickenBreedOrganism: Coding = {
  code: '132728009',
  display: 'Styrian chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OldEnglishGameChickenBreedOrganism: Coding = {
  code: '132729001',
  display: 'Old English Game chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AppenzellBeardedHenChickenBreedOrganism: Coding = {
  code: '132730006',
  display: 'Appenzell Bearded Hen chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BadenWurttemburgHorseBreedOrganism: Coding = {
  code: '132731005',
  display: 'Baden Wurttemburg horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BritishWarmbloodHorseBreedOrganism: Coding = {
  code: '132732003',
  display: 'British Warmblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IsraeliHorseBreedOrganism: Coding = {
  code: '132733008',
  display: 'Israeli horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FrenchArdennaisHorseBreedOrganism: Coding = {
  code: '132734002',
  display: 'French Ardennais horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BooroolaMerinoSheepBreedOrganism: Coding = {
  code: '132735001',
  display: 'Booroola Merino sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CukurovaHorseBreedOrganism: Coding = {
  code: '132736000',
  display: 'Cukurova horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CzechColdbloodHorseBreedOrganism: Coding = {
  code: '132737009',
  display: 'Czech Coldblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CzechoslovakianSmallRidingHorseHorseBreedOrganism: Coding = {
  code: '132738004',
  display: 'Czechoslovakian Small Riding Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JianchangHorseBreedOrganism: Coding = {
  code: '132739007',
  display: 'Jianchang horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JielinHorseBreedOrganism: Coding = {
  code: '132740009',
  display: 'Jielin horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WielkopolskiHorseBreedOrganism: Coding = {
  code: '132741008',
  display: 'Wielkopolski horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EleiaHorseBreedOrganism: Coding = {
  code: '132742001',
  display: 'Eleia horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EnglishCobHorseBreedOrganism: Coding = {
  code: '132743006',
  display: 'English Cob horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WelshPonyHorseBreedOrganism: Coding = {
  code: '132744000',
  display: 'Welsh Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WelshPonyOfCobTypeHorseBreedOrganism: Coding = {
  code: '132745004',
  display: 'Welsh Pony of Cob Type horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EnglishHunterHorseBreedOrganism: Coding = {
  code: '132746003',
  display: 'English Hunter horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EriskayPonyHorseBreedOrganism: Coding = {
  code: '132747007',
  display: 'Eriskay Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HackneyPonyHorseBreedOrganism: Coding = {
  code: '132748002',
  display: 'Hackney Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EstonianDraftHorseBreedOrganism: Coding = {
  code: '132749005',
  display: 'Estonian Draft horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HeiheHorseBreedOrganism: Coding = {
  code: '132750005',
  display: 'Heihe horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HeilongkaingHorseBreedOrganism: Coding = {
  code: '132751009',
  display: 'Heilongkaing horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SulmtalerChickenBreedOrganism: Coding = {
  code: '132752002',
  display: 'Sulmtaler chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SvartHnaChickenBreedOrganism: Coding = {
  code: '132753007',
  display: 'Svart Höna chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VorwerksChickenBreedOrganism: Coding = {
  code: '132754001',
  display: 'Vorwerks chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WestfalischeTotlegerChickenBreedOrganism: Coding = {
  code: '132755000',
  display: 'Westfalische Totleger chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YamatoGunkeiChickenBreedOrganism: Coding = {
  code: '132756004',
  display: 'Yamato Gunkei chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanishSportPonyHorseBreedOrganism: Coding = {
  code: '132757008',
  display: 'Danish Sport Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KabardaHorseBreedOrganism: Coding = {
  code: '132758003',
  display: 'Kabarda horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KalmykHorseBreedOrganism: Coding = {
  code: '132759006',
  display: 'Kalmyk horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MangalargaMarchadorHorseBreedOrganism: Coding = {
  code: '132760001',
  display: 'Mangalarga Marchador horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DonHorseBreedOrganism: Coding = {
  code: '132761002',
  display: 'Don horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ManipuriHorseBreedOrganism: Coding = {
  code: '132762009',
  display: 'Manipuri horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwissWarmbloodHorseBreedOrganism: Coding = {
  code: '132763004',
  display: 'Swiss Warmblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TavdaHorseBreedOrganism: Coding = {
  code: '132764005',
  display: 'Tavda horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EastBulgarianHorseBreedOrganism: Coding = {
  code: '132765006',
  display: 'East Bulgarian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EastFriesianOldTypeHorseBreedOrganism: Coding = {
  code: '132766007',
  display: 'East Friesian (Old Type) horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EastFriesianWarmbloodModernTypeHorseBreedOrganism: Coding = {
  code: '132767003',
  display: 'East Friesian Warmblood (Modern Type) horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KakhetianPigBreedOrganism: Coding = {
  code: '132768008',
  display: 'Kakhetian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WestFrenchWhitePigBreedOrganism: Coding = {
  code: '132769000',
  display: 'West French White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MiniatureHerefordCattleBreedOrganism: Coding = {
  code: '132770004',
  display: 'Miniature Hereford cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JemJemZebuCattleBreedOrganism: Coding = {
  code: '132771000',
  display: 'Jem-Jem Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MinusinHorseBreedOrganism: Coding = {
  code: '132772007',
  display: 'Minusin horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MorochucoHorseBreedOrganism: Coding = {
  code: '132773002',
  display: 'Morochuco horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FrenchTrotterHorseBreedOrganism: Coding = {
  code: '132774008',
  display: 'French Trotter horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FuriosoHorseBreedOrganism: Coding = {
  code: '132775009',
  display: 'Furioso horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MurgheseHorseBreedOrganism: Coding = {
  code: '132776005',
  display: 'Murghese horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MytileneHorseBreedOrganism: Coding = {
  code: '132777001',
  display: 'Mytilene horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NamibDesertHorseHorseBreedOrganism: Coding = {
  code: '132778006',
  display: 'Namib Desert Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanishOldenborgHorseBreedOrganism: Coding = {
  code: '132779003',
  display: 'Danish Oldenborg horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VolynskCattleBreedOrganism: Coding = {
  code: '132780000',
  display: 'Volynsk cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SenepolCattleBreedOrganism: Coding = {
  code: '132781001',
  display: 'Senepol cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShillukCattleBreedOrganism: Coding = {
  code: '132782008',
  display: 'Shilluk cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SarPlaninaSheepBreedOrganism: Coding = {
  code: '132783003',
  display: 'Sar Planina sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SantaInsSheepBreedOrganism: Coding = {
  code: '132784009',
  display: 'Santa Inês sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SahelTypeSheepBreedOrganism: Coding = {
  code: '132785005',
  display: 'Sahel-type sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RygjaSheepBreedOrganism: Coding = {
  code: '132786006',
  display: 'Rygja sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RyaSheepBreedOrganism: Coding = {
  code: '132787002',
  display: 'Rya sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MoghaniSheepBreedOrganism: Coding = {
  code: '132788007',
  display: 'Moghani sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RougeDeLQuoteQuestSheepBreedOrganism: Coding = {
  code: '132789004',
  display: "Rouge de l'Quest sheep breed (organism)",
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SoaySheepBreedOrganism: Coding = {
  code: '132790008',
  display: 'Soay sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthSuffolkSheepBreedOrganism: Coding = {
  code: '132791007',
  display: 'South Suffolk sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthWalesMountainSheepBreedOrganism: Coding = {
  code: '132792000',
  display: 'South Wales Mountain sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SplsauSheepBreedOrganism: Coding = {
  code: '132793005',
  display: 'Spælsau sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SpiegelSheepBreedOrganism: Coding = {
  code: '132794004',
  display: 'Spiegel sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_StCroixSheepBreedOrganism: Coding = {
  code: '132795003',
  display: 'St. Croix sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SteigarSheepBreedOrganism: Coding = {
  code: '132796002',
  display: 'Steigar sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SteinschafSheepBreedOrganism: Coding = {
  code: '132797006',
  display: 'Steinschaf sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WelshMountainSheepBreedOrganism: Coding = {
  code: '132798001',
  display: 'Welsh Mountain sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwedishFurSheepBreedOrganism: Coding = {
  code: '132799009',
  display: 'Swedish Fur Sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TeeswaterSheepBreedOrganism: Coding = {
  code: '132800008',
  display: 'Teeswater sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TexelSheepBreedOrganism: Coding = {
  code: '132801007',
  display: 'Texel sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PelibeySheepBreedOrganism: Coding = {
  code: '132802000',
  display: 'Pelibüey sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MoradaNovaSheepBreedOrganism: Coding = {
  code: '132803005',
  display: 'Morada Nova sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BalkhiSheepBreedOrganism: Coding = {
  code: '132804004',
  display: 'Balkhi sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BavarianForestSheepBreedOrganism: Coding = {
  code: '132805003',
  display: 'Bavarian Forest sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BarbadosBlackbellySheepBreedOrganism: Coding = {
  code: '132806002',
  display: 'Barbados Blackbelly sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomneySheepBreedOrganism: Coding = {
  code: '132807006',
  display: 'Romney sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AwassiSheepBreedOrganism: Coding = {
  code: '132808001',
  display: 'Awassi sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArapawaIslandSheepBreedOrganism: Coding = {
  code: '132809009',
  display: 'Arapawa Island sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArabiSheepBreedOrganism: Coding = {
  code: '132810004',
  display: 'Arabi sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ApennineSheepBreedOrganism: Coding = {
  code: '132811000',
  display: 'Apennine sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanTunisSheepBreedOrganism: Coding = {
  code: '132812007',
  display: 'American Tunis sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BalwenWelshMountainSheepBreedOrganism: Coding = {
  code: '132813002',
  display: 'Balwen Welsh Mountain sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PrianganSheepBreedOrganism: Coding = {
  code: '132814008',
  display: 'Priangan sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RaboLargoSheepBreedOrganism: Coding = {
  code: '132815009',
  display: 'Rabo Largo sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FrieslandsChickenBreedOrganism: Coding = {
  code: '132816005',
  display: 'Frieslands chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmeraucanaChickenBreedOrganism: Coding = {
  code: '132817001',
  display: 'Ameraucana chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SbosChickenBreedOrganism: Coding = {
  code: '132818006',
  display: 'Åsbos chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AugsburgerChickenBreedOrganism: Coding = {
  code: '132819003',
  display: 'Augsburger chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BielefelderChickenBreedOrganism: Coding = {
  code: '132820009',
  display: 'Bielefelder chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorfolkGrayChickenBreedOrganism: Coding = {
  code: '132821008',
  display: 'Norfolk Gray chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HedemoraChickenBreedOrganism: Coding = {
  code: '132822001',
  display: 'Hedemora chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HungarianYellowChickenBreedOrganism: Coding = {
  code: '132823006',
  display: 'Hungarian Yellow chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IowaBlueChickenBreedOrganism: Coding = {
  code: '132824000',
  display: 'Iowa blue chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedJungleFowlTypeChickenOrganism: Coding = {
  code: '132825004',
  display: 'Red jungle fowl type chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KoShamoChickenBreedOrganism: Coding = {
  code: '132826003',
  display: 'Ko Shamo chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KraienkoppeChickenBreedOrganism: Coding = {
  code: '132827007',
  display: 'Kraienkoppe chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LincolnshireBuffChickenBreedOrganism: Coding = {
  code: '132828002',
  display: 'Lincolnshire Buff chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TomaruChickenBreedOrganism: Coding = {
  code: '132829005',
  display: 'Tomaru chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OnagadoriChickenBreedOrganism: Coding = {
  code: '132830000',
  display: 'Onagadori chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ManxRumpiesChickenBreedOrganism: Coding = {
  code: '132831001',
  display: 'Manx Rumpies chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MalineChickenBreedOrganism: Coding = {
  code: '132832008',
  display: 'Maline chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NankinChickenBreedOrganism: Coding = {
  code: '132833003',
  display: 'Nankin chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LandChickenBreedOrganism: Coding = {
  code: '132834009',
  display: 'Øland chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LanskBantamChickenBreedOrganism: Coding = {
  code: '132835005',
  display: 'Ölansk Bantam chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OrustChickenBreedOrganism: Coding = {
  code: '132836006',
  display: 'Orust chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PenedesencaChickenBreedOrganism: Coding = {
  code: '132837002',
  display: 'Penedesenca chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RhodeIslandWhiteChickenBreedOrganism: Coding = {
  code: '132838007',
  display: 'Rhode Island White chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RosecombBantamChickenBreedOrganism: Coding = {
  code: '132839004',
  display: 'Rosecomb Bantam chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SablepootChickenBreedOrganism: Coding = {
  code: '132840002',
  display: 'Sablepoot chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SaipanJungleFowlChickenBreedOrganism: Coding = {
  code: '132841003',
  display: 'Saipan Jungle Fowl chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ScotsDumpyChickenBreedOrganism: Coding = {
  code: '132842005',
  display: 'Scots Dumpy chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MubanPigBreedOrganism: Coding = {
  code: '132843000',
  display: 'Muban pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IbanPigBreedOrganism: Coding = {
  code: '132844006',
  display: 'Iban pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AltaySheepBreedOrganism: Coding = {
  code: '132845007',
  display: 'Altay sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FaeroesSheepBreedOrganism: Coding = {
  code: '132846008',
  display: 'Faeroes sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GreenChickenBreedOrganism: Coding = {
  code: '132847004',
  display: 'Green chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GallusInaurisChickenBreedOrganism: Coding = {
  code: '132848009',
  display: 'Gallus Inauris chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PittIslandSheepBreedOrganism: Coding = {
  code: '132849001',
  display: 'Pitt Island sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GoldenMontazahChickenBreedOrganism: Coding = {
  code: '132850001',
  display: 'Golden Montazah chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PinziritaSheepBreedOrganism: Coding = {
  code: '132851002',
  display: 'Pinzirita sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SardinianSheepBreedOrganism: Coding = {
  code: '132852009',
  display: 'Sardinian sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EastFriesianSheepBreedOrganism: Coding = {
  code: '132853004',
  display: 'East Friesian sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_UjumqinSheepBreedOrganism: Coding = {
  code: '132854005',
  display: 'Ujumqin sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DLSSheepBreedOrganism: Coding = {
  code: '132855006',
  display: 'DLS sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WalachenschafSheepBreedOrganism: Coding = {
  code: '132856007',
  display: 'Walachenschaf sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OutaouaisArcottSheepBreedOrganism: Coding = {
  code: '132857003',
  display: 'Outaouais Arcott sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OssimiSheepBreedOrganism: Coding = {
  code: '132858008',
  display: 'Ossimi sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BentheimerLandschafSheepBreedOrganism: Coding = {
  code: '132859000',
  display: 'Bentheimer Landschaf sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BarbadoSheepBreedOrganism: Coding = {
  code: '132860005',
  display: 'Barbado sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BaluchiSheepBreedOrganism: Coding = {
  code: '132861009',
  display: 'Baluchi sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShamoChickenBreedOrganism: Coding = {
  code: '132862002',
  display: 'Shamo chicken breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanIndianHorseHorseBreedOrganism: Coding = {
  code: '132951001',
  display: 'American Indian Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanMustangHorseBreedOrganism: Coding = {
  code: '132952008',
  display: 'American Mustang horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanQuarterHorseHorseBreedOrganism: Coding = {
  code: '132953003',
  display: 'American Quarter Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanShetlandPonyHorseBreedOrganism: Coding = {
  code: '132954009',
  display: 'American Shetland pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AnadoluHorseBreedOrganism: Coding = {
  code: '132955005',
  display: 'Anadolu horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AndeanHorseBreedOrganism: Coding = {
  code: '132956006',
  display: 'Andean horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AngloKabardaHorseBreedOrganism: Coding = {
  code: '132957002',
  display: 'Anglo-Kabarda horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PoitouDonkeyBreedOrganism: Coding = {
  code: '132958007',
  display: 'Poitou Donkey breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MammothJackStockDonkeyBreedOrganism: Coding = {
  code: '132959004',
  display: 'Mammoth Jack Stock Donkey breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NarymHorseBreedOrganism: Coding = {
  code: '132960009',
  display: 'Narym horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NationalSpottedSaddleHorseHorseBreedOrganism: Coding = {
  code: '132961008',
  display: 'National Spotted Saddle Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NigerianHorseBreedOrganism: Coding = {
  code: '132962001',
  display: 'Nigerian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorthSwedishTrotterHorseBreedOrganism: Coding = {
  code: '132963006',
  display: 'North Swedish Trotter horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OrientalHorseHorseBreedOrganism: Coding = {
  code: '132964000',
  display: 'Oriental Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RhinelandHeavyDraftHorseBreedOrganism: Coding = {
  code: '132965004',
  display: 'Rhineland Heavy Draft horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomanianSaddleHorseHorseBreedOrganism: Coding = {
  code: '132966003',
  display: 'Romanian Saddle Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RottalHorseBreedOrganism: Coding = {
  code: '132967007',
  display: 'Rottal horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RoyalCanadianMountedPoliceHorseHorseBreedOrganism: Coding = {
  code: '132968002',
  display: 'Royal Canadian Mounted Police Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RussianSaddleHorseHorseBreedOrganism: Coding = {
  code: '132969005',
  display: 'Russian Saddle Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SableIslandHorseHorseBreedOrganism: Coding = {
  code: '132970006',
  display: 'Sable Island Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PanjeHorseBreedOrganism: Coding = {
  code: '132971005',
  display: 'Panje horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PatibarcinaHorseBreedOrganism: Coding = {
  code: '132972003',
  display: 'Patibarcina horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PechoraHorseBreedOrganism: Coding = {
  code: '132973008',
  display: 'Pechora horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PeneiaHorseBreedOrganism: Coding = {
  code: '132974002',
  display: 'Peneia horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PerianganHorseBreedOrganism: Coding = {
  code: '132975001',
  display: 'Periangan horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PersianArabHorseBreedOrganism: Coding = {
  code: '132976000',
  display: 'Persian Arab horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PetisoArgentinoHorseBreedOrganism: Coding = {
  code: '132977009',
  display: 'Petiso Argentino horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolishDraftHorseBreedOrganism: Coding = {
  code: '132978004',
  display: 'Polish Draft horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PriobHorseBreedOrganism: Coding = {
  code: '132979007',
  display: 'Priob horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RahvanHorseBreedOrganism: Coding = {
  code: '132980005',
  display: 'Rahvan horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SalernoHorseBreedOrganism: Coding = {
  code: '132981009',
  display: 'Salerno horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SandalwoodHorseBreedOrganism: Coding = {
  code: '132982002',
  display: 'Sandalwood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SandanHorseBreedOrganism: Coding = {
  code: '132983007',
  display: 'Sandan horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PindosHorseBreedOrganism: Coding = {
  code: '132984001',
  display: 'Pindos horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PiquiraPonyHorseBreedOrganism: Coding = {
  code: '132985000',
  display: 'Piquira Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PlevenHorseBreedOrganism: Coding = {
  code: '132986004',
  display: 'Pleven horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LargeStandardDonkeyOrganism: Coding = {
  code: '132987008',
  display: 'Large standard donkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_StandardDonkeyOrganism: Coding = {
  code: '132988003',
  display: 'Standard donkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MiniatureDonkeyOrganism: Coding = {
  code: '132989006',
  display: 'Miniature donkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GarranoTarpanHorseXDomesticHorseBreedOrganism: Coding = {
  code: '132990002',
  display: 'Garrano tarpan horse X domestic horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KoninkTarpanHorseXDomesticHorseBreedOrganism: Coding = {
  code: '132991003',
  display: 'Konink tarpan horse X domestic horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AsturianTarpanHorseXDomesticHorseBreedOrganism: Coding = {
  code: '132992005',
  display: 'Asturian tarpan horse X domestic horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PottokTarpanHorseXDomesticHorseBreedOrganism: Coding = {
  code: '132993000',
  display: 'Pottok tarpan horse X domestic horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RussianTrotterHorseBreedOrganism: Coding = {
  code: '132994006',
  display: 'Russian Trotter horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WestAfricanBarbHorseBreedOrganism: Coding = {
  code: '132995007',
  display: 'West African Barb horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FellPonyHorseBreedOrganism: Coding = {
  code: '132996008',
  display: 'Fell Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NationalShowHorseHorseBreedOrganism: Coding = {
  code: '132997004',
  display: 'National Show Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ZhemaichuHorseBreedOrganism: Coding = {
  code: '132998009',
  display: 'Zhemaichu horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YonaguniHorseBreedOrganism: Coding = {
  code: '132999001',
  display: 'Yonaguni horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YakutHorseBreedOrganism: Coding = {
  code: '133000000',
  display: 'Yakut horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TawleedHorseBreedOrganism: Coding = {
  code: '133001001',
  display: 'Tawleed horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WesternSudanPonyHorseBreedOrganism: Coding = {
  code: '133002008',
  display: 'Western Sudan Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WeleraPonyHorseBreedOrganism: Coding = {
  code: '133003003',
  display: 'Welera Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VyatkaHorseBreedOrganism: Coding = {
  code: '133004009',
  display: 'Vyatka horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VladimirHeavyDraftHorseBreedOrganism: Coding = {
  code: '133005005',
  display: 'Vladimir Heavy Draft horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VlaamperdHorseBreedOrganism: Coding = {
  code: '133006006',
  display: 'Vlaamperd horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_UkrainianSaddleHorseHorseBreedOrganism: Coding = {
  code: '133007002',
  display: 'Ukrainian Saddle Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ToriHorseBreedOrganism: Coding = {
  code: '133008007',
  display: 'Tori horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TokaraHorseBreedOrganism: Coding = {
  code: '133009004',
  display: 'Tokara horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NewKirgizHorseBreedOrganism: Coding = {
  code: '133010009',
  display: 'New Kirgiz horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OldenburgHorseBreedOrganism: Coding = {
  code: '133011008',
  display: 'Oldenburg horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MisakiHorseBreedOrganism: Coding = {
  code: '133012001',
  display: 'Misaki horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MiyakoHorseBreedOrganism: Coding = {
  code: '133013006',
  display: 'Miyako horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MongolianHorseBreedOrganism: Coding = {
  code: '133014000',
  display: 'Mongolian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WalerHorseBreedOrganism: Coding = {
  code: '133015004',
  display: 'Waler horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DutchDraftHorseBreedOrganism: Coding = {
  code: '133016003',
  display: 'Dutch Draft horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EgyptianHorseBreedOrganism: Coding = {
  code: '133017007',
  display: 'Egyptian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EstonianNativeHorseBreedOrganism: Coding = {
  code: '133018002',
  display: 'Estonian Native horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ExmoorPonyHorseBreedOrganism: Coding = {
  code: '133019005',
  display: 'Exmoor Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FaeroesIslandHorseHorseBreedOrganism: Coding = {
  code: '133020004',
  display: 'Faeroes Island Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FalabellaHorseBreedOrganism: Coding = {
  code: '133021000',
  display: 'Falabella horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DutchWarmbloodHorseBreedOrganism: Coding = {
  code: '133022007',
  display: 'Dutch Warmblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DongolaHorseBreedOrganism: Coding = {
  code: '133023002',
  display: 'Dongola horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DleHorseBreedOrganism: Coding = {
  code: '133024008',
  display: 'Døle horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DjermaHorseBreedOrganism: Coding = {
  code: '133025009',
  display: 'Djerma horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DelibozHorseBreedOrganism: Coding = {
  code: '133026005',
  display: 'Deliboz horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DartmoorPonyHorseBreedOrganism: Coding = {
  code: '133027001',
  display: 'Dartmoor Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CriouloHorseBreedOrganism: Coding = {
  code: '133028006',
  display: 'Crioulo horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FinnhorseHorseBreedOrganism: Coding = {
  code: '133029003',
  display: 'Finnhorse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SanfratelloHorseBreedOrganism: Coding = {
  code: '133030008',
  display: 'Sanfratello horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MorabHorseBreedOrganism: Coding = {
  code: '133031007',
  display: 'Morab horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MoyleHorseBreedOrganism: Coding = {
  code: '133032000',
  display: 'Moyle horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MustangHorseBreedOrganism: Coding = {
  code: '133033005',
  display: 'Mustang horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MQuoteBayarHorseBreedOrganism: Coding = {
  code: '133034004',
  display: "M'Bayar horse breed (organism)",
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LusitanoHorseBreedOrganism: Coding = {
  code: '133035003',
  display: 'Lusitano horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NewfoundlandPonyHorseBreedOrganism: Coding = {
  code: '133036002',
  display: 'Newfoundland Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NomaHorseBreedOrganism: Coding = {
  code: '133037006',
  display: 'Noma horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NooitgedachtPonyHorseBreedOrganism: Coding = {
  code: '133038001',
  display: 'Nooitgedacht Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NordlandHorseBreedOrganism: Coding = {
  code: '133039009',
  display: 'Nordland horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NoricHorseBreedOrganism: Coding = {
  code: '133040006',
  display: 'Noric horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorthSwedishHorseHorseBreedOrganism: Coding = {
  code: '133041005',
  display: 'North Swedish Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NortheasternHorseBreedOrganism: Coding = {
  code: '133042003',
  display: 'Northeastern horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KisberFelverHorseBreedOrganism: Coding = {
  code: '133043008',
  display: 'Kisber Felver horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AngloArabHorseBreedOrganism: Coding = {
  code: '133044002',
  display: 'Anglo-Arab horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NoniusHorseBreedOrganism: Coding = {
  code: '133045001',
  display: 'Nonius horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NooitgedachtHorseBreedOrganism: Coding = {
  code: '133046000',
  display: 'Nooitgedacht horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IomudHorseBreedOrganism: Coding = {
  code: '133047009',
  display: 'Iomud horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JutlandHorseBreedOrganism: Coding = {
  code: '133048004',
  display: 'Jutland horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KarabairHorseBreedOrganism: Coding = {
  code: '133049007',
  display: 'Karabair horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KarabakhHorseBreedOrganism: Coding = {
  code: '133050007',
  display: 'Karabakh horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KazakhHorseBreedOrganism: Coding = {
  code: '133051006',
  display: 'Kazakh horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MangalargaHorseBreedOrganism: Coding = {
  code: '133052004',
  display: 'Mangalarga horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KirdiPonyHorseBreedOrganism: Coding = {
  code: '133053009',
  display: 'Kirdi Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KisoHorseBreedOrganism: Coding = {
  code: '133054003',
  display: 'Kiso horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KladrubyHorseBreedOrganism: Coding = {
  code: '133055002',
  display: 'Kladruby horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KnabstrupHorseBreedOrganism: Coding = {
  code: '133056001',
  display: 'Knabstrup horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KushumHorseBreedOrganism: Coding = {
  code: '133057005',
  display: 'Kushum horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KustanaiHorseBreedOrganism: Coding = {
  code: '133058000',
  display: 'Kustanai horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LatvianHorseBreedOrganism: Coding = {
  code: '133059008',
  display: 'Latvian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LithuanianHeavyDraftHorseBreedOrganism: Coding = {
  code: '133060003',
  display: 'Lithuanian Heavy Draft horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LokaiHorseBreedOrganism: Coding = {
  code: '133061004',
  display: 'Lokai horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KigerMustangHorseBreedOrganism: Coding = {
  code: '133062006',
  display: 'Kiger Mustang horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PonyOfTheAmericasHorseBreedOrganism: Coding = {
  code: '133063001',
  display: 'Pony of the Americas horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PintabianHorseBreedOrganism: Coding = {
  code: '133064007',
  display: 'Pintabian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PantaneiroHorseBreedOrganism: Coding = {
  code: '133065008',
  display: 'Pantaneiro horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OrlovTrotterHorseBreedOrganism: Coding = {
  code: '133066009',
  display: 'Orlov Trotter horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorthernArdennaisHorseBreedOrganism: Coding = {
  code: '133067000',
  display: 'Northern Ardennais horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AbtenauerHorseBreedOrganism: Coding = {
  code: '133068005',
  display: 'Abtenauer horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AdaevHorseBreedOrganism: Coding = {
  code: '133069002',
  display: 'Adaev horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlbanianHorseBreedOrganism: Coding = {
  code: '133070001',
  display: 'Albanian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlterRealHorseBreedOrganism: Coding = {
  code: '133071002',
  display: 'Alter Real horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanBashkirCurlyHorseBreedOrganism: Coding = {
  code: '133072009',
  display: 'American Bashkir Curly horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PoitouMuleProducerHorseBreedOrganism: Coding = {
  code: '133073004',
  display: 'Poitou Mule Producer horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolesianHorseBreedOrganism: Coding = {
  code: '133074005',
  display: 'Polesian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SardinianAngloArabHorseBreedOrganism: Coding = {
  code: '133075006',
  display: 'Sardinian Anglo-Arab horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SardinianPonyHorseBreedOrganism: Coding = {
  code: '133076007',
  display: 'Sardinian Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SarvarHorseBreedOrganism: Coding = {
  code: '133077003',
  display: 'Sarvar horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SchleswigHorseBreedOrganism: Coding = {
  code: '133078008',
  display: 'Schleswig horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SchwarzwalderFuchseHorseBreedOrganism: Coding = {
  code: '133079000',
  display: 'Schwarzwalder Fuchse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SenneHorseBreedOrganism: Coding = {
  code: '133080002',
  display: 'Senne horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShanHorseBreedOrganism: Coding = {
  code: '133081003',
  display: 'Shan horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SilesianHorseBreedOrganism: Coding = {
  code: '133082005',
  display: 'Silesian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SiniHorseBreedOrganism: Coding = {
  code: '133083000',
  display: 'Sini horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SkyrosHorseBreedOrganism: Coding = {
  code: '133084006',
  display: 'Skyros horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SlovakWarmbloodHorseBreedOrganism: Coding = {
  code: '133085007',
  display: 'Slovak Warmblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SokolkaHorseBreedOrganism: Coding = {
  code: '133086008',
  display: 'Sokolka horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthAfricanMiniatureHorseBreedOrganism: Coding = {
  code: '133087004',
  display: 'South African Miniature horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthGermanColdbloodHorseBreedOrganism: Coding = {
  code: '133088009',
  display: 'South German Coldblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthwestSpanishMustangHorseBreedOrganism: Coding = {
  code: '133089001',
  display: 'Southwest Spanish Mustang horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SpanishAmericanHorseHorseBreedOrganism: Coding = {
  code: '133090005',
  display: 'Spanish-American Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SpanishAngloArabHorseBreedOrganism: Coding = {
  code: '133091009',
  display: 'Spanish Anglo-Arab horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SpanishColonialHorseHorseBreedOrganism: Coding = {
  code: '133092002',
  display: 'Spanish Colonial Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SpitiHorseBreedOrganism: Coding = {
  code: '133093007',
  display: 'Spiti horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SulawesiHorseBreedOrganism: Coding = {
  code: '133094001',
  display: 'Sulawesi horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CriolloHorseBreedOrganism: Coding = {
  code: '133095000',
  display: 'Criollo horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HequHorseBreedOrganism: Coding = {
  code: '133096004',
  display: 'Hequ horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ConnemaraPonyHorseBreedOrganism: Coding = {
  code: '133097008',
  display: 'Connemara Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ColoradoRangerHorseBreedOrganism: Coding = {
  code: '133098003',
  display: 'Colorado Ranger horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DalesPonyHorseBreedOrganism: Coding = {
  code: '133099006',
  display: 'Dales Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GotlandHorseBreedOrganism: Coding = {
  code: '133100003',
  display: 'Gotland horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChincoteaguePonyHorseBreedOrganism: Coding = {
  code: '133101004',
  display: 'Chincoteague Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HokkaidoHorseBreedOrganism: Coding = {
  code: '133102006',
  display: 'Hokkaido horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HighlandPonyHorseBreedOrganism: Coding = {
  code: '133103001',
  display: 'Highland Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GroningenHorseBreedOrganism: Coding = {
  code: '133104007',
  display: 'Groningen horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CubanPintoHorseBreedOrganism: Coding = {
  code: '133105008',
  display: 'Cuban Pinto horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FleuveHorseBreedOrganism: Coding = {
  code: '133106009',
  display: 'Fleuve horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GoldenAmericanSaddlebredHorseBreedOrganism: Coding = {
  code: '133107000',
  display: 'Golden American Saddlebred horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GidranHorseBreedOrganism: Coding = {
  code: '133108005',
  display: 'Gidran horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GelderlandHorseBreedOrganism: Coding = {
  code: '133109002',
  display: 'Gelderland horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GalicianPonyHorseBreedOrganism: Coding = {
  code: '133110007',
  display: 'Galician Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FriesianHorseBreedOrganism: Coding = {
  code: '133111006',
  display: 'Friesian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FrederiksborgHorseBreedOrganism: Coding = {
  code: '133112004',
  display: 'Frederiksborg horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FoutaHorseBreedOrganism: Coding = {
  code: '133113009',
  display: 'Fouta horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FloridaCrackerHorseBreedOrganism: Coding = {
  code: '133114003',
  display: 'Florida Cracker horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuangxiHorseBreedOrganism: Coding = {
  code: '133115002',
  display: 'Guangxi horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArdennesHorseBreedOrganism: Coding = {
  code: '133116001',
  display: 'Ardennes horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanWalkingPonyHorseBreedOrganism: Coding = {
  code: '133117005',
  display: 'American Walking Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AztecaHorseBreedOrganism: Coding = {
  code: '133118000',
  display: 'Azteca horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanCreamDraftHorseBreedOrganism: Coding = {
  code: '133119008',
  display: 'American Cream Draft horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AltaiHorseBreedOrganism: Coding = {
  code: '133120002',
  display: 'Altai horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AkhalTekeHorseBreedOrganism: Coding = {
  code: '133121003',
  display: 'Akhal-Teke horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AbyssinianHorseBreedOrganism: Coding = {
  code: '133122005',
  display: 'Abyssinian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BhirumPonyHorseBreedOrganism: Coding = {
  code: '133123000',
  display: 'Bhirum Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChejuHorseBreedOrganism: Coding = {
  code: '133124006',
  display: 'Cheju horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CayuseHorseBreedOrganism: Coding = {
  code: '133125007',
  display: 'Cayuse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CaspianHorseBreedOrganism: Coding = {
  code: '133126008',
  display: 'Caspian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CarthusianHorseBreedOrganism: Coding = {
  code: '133127004',
  display: 'Carthusian horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CampolinaHorseBreedOrganism: Coding = {
  code: '133128009',
  display: 'Campolina horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ByelorussianHarnessHorseBreedOrganism: Coding = {
  code: '133129001',
  display: 'Byelorussian Harness horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BudyonnyHorseBreedOrganism: Coding = {
  code: '133130006',
  display: 'Budyonny horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustralianBrumbyHorseBreedOrganism: Coding = {
  code: '133131005',
  display: 'Australian Brumby horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustralianStockHorseHorseBreedOrganism: Coding = {
  code: '133132003',
  display: 'Australian Stock Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BasutoPonyHorseBreedOrganism: Coding = {
  code: '133133008',
  display: 'Basuto Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BashkirCurlyHorseBreedOrganism: Coding = {
  code: '133134002',
  display: 'Bashkir Curly horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BashkirHorseBreedOrganism: Coding = {
  code: '133135001',
  display: 'Bashkir horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BarbHorseBreedOrganism: Coding = {
  code: '133136000',
  display: 'Barb horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BanEiHorseBreedOrganism: Coding = {
  code: '133137009',
  display: 'Ban-ei horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CarpathianPonyHorseBreedOrganism: Coding = {
  code: '133138004',
  display: 'Carpathian Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BaluchiHorseBreedOrganism: Coding = {
  code: '133139007',
  display: 'Baluchi horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BalearicHorseBreedOrganism: Coding = {
  code: '133140009',
  display: 'Balearic horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChileanCorraleroHorseBreedOrganism: Coding = {
  code: '133141008',
  display: 'Chilean Corralero horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BretonHorseBreedOrganism: Coding = {
  code: '133142001',
  display: 'Breton horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TaishuhHorseBreedOrganism: Coding = {
  code: '133143006',
  display: 'Taishuh horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwedishWarmbloodHorseBreedOrganism: Coding = {
  code: '133144000',
  display: 'Swedish Warmblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SudanCountryBredHorseBreedOrganism: Coding = {
  code: '133145004',
  display: 'Sudan Country-Bred horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SpanishNormanHorseBreedOrganism: Coding = {
  code: '133146003',
  display: 'Spanish-Norman horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SpanishBarbHorseBreedOrganism: Coding = {
  code: '133147007',
  display: 'Spanish Barb horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SovietHeavyDraftHorseBreedOrganism: Coding = {
  code: '133148002',
  display: 'Soviet Heavy Draft horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SorraiaHorseBreedOrganism: Coding = {
  code: '133149005',
  display: 'Sorraia horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SomaliPonyHorseBreedOrganism: Coding = {
  code: '133150005',
  display: 'Somali Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TerskHorseBreedOrganism: Coding = {
  code: '133151009',
  display: 'Tersk horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShagyaHorseBreedOrganism: Coding = {
  code: '133152002',
  display: 'Shagya horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SelleFrancaisHorseBreedOrganism: Coding = {
  code: '133153007',
  display: 'Selle Francais horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SanheHorseBreedOrganism: Coding = {
  code: '133154001',
  display: 'Sanhe horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RussianHeavyDraftHorseBreedOrganism: Coding = {
  code: '133155000',
  display: 'Russian Heavy Draft horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RockyMountainHorseHorseBreedOrganism: Coding = {
  code: '133156004',
  display: 'Rocky Mountain Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RackingHorseHorseBreedOrganism: Coding = {
  code: '133157008',
  display: 'Racking Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_QuarterPonyHorseBreedOrganism: Coding = {
  code: '133158003',
  display: 'Quarter Pony horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_QuarabHorseBreedOrganism: Coding = {
  code: '133159006',
  display: 'Quarab horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SingleFootingHorseHorseBreedOrganism: Coding = {
  code: '133160001',
  display: 'Single-Footing Horse horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TuyHoaHairlessPigBreedOrganism: Coding = {
  code: '133161002',
  display: 'Tuy Hoa Hairless pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HainanPigBreedOrganism: Coding = {
  code: '133162009',
  display: 'Hainan pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SinoVietnamesePigBreedOrganism: Coding = {
  code: '133163004',
  display: 'Sino-Vietnamese pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoXuPigBreedOrganism: Coding = {
  code: '133164005',
  display: 'Bo Xu pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThuocNhieuPigBreedOrganism: Coding = {
  code: '133165006',
  display: 'Thuoc Nhieu pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BurmesePigBreedOrganism: Coding = {
  code: '133166007',
  display: 'Burmese pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChinPigBreedOrganism: Coding = {
  code: '133167003',
  display: 'Chin pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SiamesePigBreedOrganism: Coding = {
  code: '133168008',
  display: 'Siamese pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HailumPigBreedOrganism: Coding = {
  code: '133169000',
  display: 'Hailum pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KwaiPigBreedOrganism: Coding = {
  code: '133170004',
  display: 'Kwai pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RaadPigBreedOrganism: Coding = {
  code: '133171000',
  display: 'Raad pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AkhaPigBreedOrganism: Coding = {
  code: '133172007',
  display: 'Akha pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthChinaPigBreedOrganism: Coding = {
  code: '133173002',
  display: 'South China pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthChinaBlackPigBreedOrganism: Coding = {
  code: '133174008',
  display: 'South China Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BalinesePigBreedOrganism: Coding = {
  code: '133175009',
  display: 'Balinese pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DianiPigBreedOrganism: Coding = {
  code: '133176005',
  display: 'Diani pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KamanPigBreedOrganism: Coding = {
  code: '133177001',
  display: 'Kaman pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AshantiDwarfPigBreedOrganism: Coding = {
  code: '133178006',
  display: 'Ashanti Dwarf pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KoronadalPigBreedOrganism: Coding = {
  code: '133179003',
  display: 'Koronadal pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OhminiPigBreedOrganism: Coding = {
  code: '133180000',
  display: 'Ohmini pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ClawnPigBreedOrganism: Coding = {
  code: '133181001',
  display: 'Clawn pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SusScrofaDomesticPigXJapaneseWildBoarIntragenusHybridOrganism: Coding = {
  code: '133182008',
  display: 'Sus scrofa domestic pig X Japanese wild boar intragenus hybrid (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KangarooIslandPigBreedOrganism: Coding = {
  code: '133183003',
  display: 'Kangaroo Island pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CaptainCookerPigBreedOrganism: Coding = {
  code: '133184009',
  display: 'Captain Cooker pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WestAfricanPigBreedOrganism: Coding = {
  code: '133185005',
  display: 'West African pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NigerianPigBreedOrganism: Coding = {
  code: '133186006',
  display: 'Nigerian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BakosiPigBreedOrganism: Coding = {
  code: '133187002',
  display: 'Bakosi pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WindsnyerPigBreedOrganism: Coding = {
  code: '133188007',
  display: 'Windsnyer pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KolbroekPigBreedOrganism: Coding = {
  code: '133189004',
  display: 'Kolbroek pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthAfricanLandracePigBreedOrganism: Coding = {
  code: '133190008',
  display: 'South African Landrace pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BulgarianWhitePigBreedOrganism: Coding = {
  code: '133191007',
  display: 'Bulgarian White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BulgarianLandracePigBreedOrganism: Coding = {
  code: '133192000',
  display: 'Bulgarian Landrace pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanubeWhitePigBreedOrganism: Coding = {
  code: '133193005',
  display: 'Danube White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DermantsiPiedPigBreedOrganism: Coding = {
  code: '133194004',
  display: 'Dermantsi Pied pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomanianNativeStocliPigBreedOrganism: Coding = {
  code: '133195003',
  display: 'Romanian Native, Stocli pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomanianNativeBaltaretPigBreedOrganism: Coding = {
  code: '133196002',
  display: 'Romanian Native, Baltaret pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BanatWhitePigBreedOrganism: Coding = {
  code: '133197006',
  display: 'Banat White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BaznaPigBreedOrganism: Coding = {
  code: '133198001',
  display: 'Bazna pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DobrogeaBlackPigBreedOrganism: Coding = {
  code: '133199009',
  display: 'Dobrogea Black pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_StreiPigBreedOrganism: Coding = {
  code: '133200007',
  display: 'Strei pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomanianLargeWhitePigBreedOrganism: Coding = {
  code: '133201006',
  display: 'Romanian Large White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomanianMeatPigPigBreedOrganism: Coding = {
  code: '133202004',
  display: 'Romanian Meat Pig pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GurktalPigBreedOrganism: Coding = {
  code: '133203009',
  display: 'Gurktal pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackSlavonianPigBreedOrganism: Coding = {
  code: '133204003',
  display: 'Black Slavonian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ResavaPigBreedOrganism: Coding = {
  code: '133205002',
  display: 'Resava pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MoravaPigBreedOrganism: Coding = {
  code: '133206001',
  display: 'Morava pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DzumaliaPigBreedOrganism: Coding = {
  code: '133207005',
  display: 'Dzumalia pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MacedonianPigBreedOrganism: Coding = {
  code: '133208000',
  display: 'Macedonian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlbanianNativePigBreedOrganism: Coding = {
  code: '133209008',
  display: 'Albanian Native pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShkodraPigBreedOrganism: Coding = {
  code: '133210003',
  display: 'Shkodra pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SlovenianWhitePigBreedOrganism: Coding = {
  code: '133211004',
  display: 'Slovenian White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SuboticaWhitePigBreedOrganism: Coding = {
  code: '133212006',
  display: 'Subotica White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PresticePigBreedOrganism: Coding = {
  code: '133213001',
  display: 'Prestice pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SlovakianBlackPiedPigBreedOrganism: Coding = {
  code: '133214007',
  display: 'Slovakian Black Pied pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CzechImprovedWhitePigBreedOrganism: Coding = {
  code: '133215008',
  display: 'Czech Improved White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MoravianLargeYorkshirePigBreedOrganism: Coding = {
  code: '133216009',
  display: 'Moravian Large Yorkshire pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SlovakianWhitePigBreedOrganism: Coding = {
  code: '133217000',
  display: 'Slovakian White pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_Slovhyb1PigBreedOrganism: Coding = {
  code: '133218005',
  display: 'Slovhyb-1 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NitraHybridPigBreedOrganism: Coding = {
  code: '133219002',
  display: 'Nitra Hybrid pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SyntheticSL98PigBreedOrganism: Coding = {
  code: '133220008',
  display: 'Synthetic SL98 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SL96PigBreedOrganism: Coding = {
  code: '133221007',
  display: 'SL96 pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CzechMeatPigBreedOrganism: Coding = {
  code: '133222000',
  display: 'Czech Meat pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CzechMiniaturePigBreedOrganism: Coding = {
  code: '133223005',
  display: 'Czech Miniature pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SmallPolishPrickEaredPigBreedOrganism: Coding = {
  code: '133224004',
  display: 'Small Polish Prick-Eared pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolesianPigBreedOrganism: Coding = {
  code: '133225003',
  display: 'Polesian pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NadbuzanskaPigBreedOrganism: Coding = {
  code: '133226002',
  display: 'Nadbuzanska pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SarnyPigBreedOrganism: Coding = {
  code: '133227006',
  display: 'Sarny pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KrolevetsPigBreedOrganism: Coding = {
  code: '133228001',
  display: 'Krolevets pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolishMarshPigBreedOrganism: Coding = {
  code: '133229009',
  display: 'Polish Marsh pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LargePolishLongEaredPigBreedOrganism: Coding = {
  code: '133230004',
  display: 'Large Polish Long-Eared pig breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HerensCattleBreedOrganism: Coding = {
  code: '133231000',
  display: 'Herens cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HinterwaldCattleBreedOrganism: Coding = {
  code: '133232007',
  display: 'Hinterwald cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HungarianGrayCattleBreedOrganism: Coding = {
  code: '133233002',
  display: 'Hungarian Gray cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IcelandicCattleBreedOrganism: Coding = {
  code: '133234008',
  display: 'Icelandic cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IllawarraCattleBreedOrganism: Coding = {
  code: '133235009',
  display: 'Illawarra cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IrishMoiledCattleBreedOrganism: Coding = {
  code: '133236005',
  display: 'Irish Moiled cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IsraeliHolsteinCattleBreedOrganism: Coding = {
  code: '133237001',
  display: 'Israeli Holstein cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IstobenCattleBreedOrganism: Coding = {
  code: '133238006',
  display: 'Istoben cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JaulanCattleBreedOrganism: Coding = {
  code: '133239003',
  display: 'Jaulan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KazakhCattleBreedOrganism: Coding = {
  code: '133240001',
  display: 'Kazakh cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KerryCattleBreedOrganism: Coding = {
  code: '133241002',
  display: 'Kerry cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KholmogoryCattleBreedOrganism: Coding = {
  code: '133242009',
  display: 'Kholmogory cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LatvianBrownCattleBreedOrganism: Coding = {
  code: '133243004',
  display: 'Latvian Brown cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LincolnRedShorthornCattleBreedOrganism: Coding = {
  code: '133244005',
  display: 'Lincoln Red Shorthorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LithuanianRedCattleBreedOrganism: Coding = {
  code: '133245006',
  display: 'Lithuanian Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MashonaCattleBreedOrganism: Coding = {
  code: '133246007',
  display: 'Mashona cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MilkingDevonCattleBreedOrganism: Coding = {
  code: '133247003',
  display: 'Milking Devon cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MirandesaCattleBreedOrganism: Coding = {
  code: '133248008',
  display: 'Mirandesa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MixedDairyCattleBreedOrganism: Coding = {
  code: '133249000',
  display: 'Mixed dairy cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MongolianCattleBreedOrganism: Coding = {
  code: '133250000',
  display: 'Mongolian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MoruchaCattleBreedOrganism: Coding = {
  code: '133251001',
  display: 'Morucha cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KurdiCattleBreedOrganism: Coding = {
  code: '133252008',
  display: 'Kurdi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NQuoteDamaCattleBreedOrganism: Coding = {
  code: '133253003',
  display: "N'dama cattle breed (organism)",
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorwegianRedCattleBreedOrganism: Coding = {
  code: '133254009',
  display: 'Norwegian Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ParthenaisCattleBreedOrganism: Coding = {
  code: '133255005',
  display: 'Parthenais cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolishRedCattleBreedOrganism: Coding = {
  code: '133256006',
  display: 'Polish Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RtienGrayCattleBreedOrganism: Coding = {
  code: '133257002',
  display: 'Rätien Gray cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedAndWhiteCattleBreedOrganism: Coding = {
  code: '133258007',
  display: 'Red and White cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedAngusCattleBreedOrganism: Coding = {
  code: '133259004',
  display: 'Red Angus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedPolledStlandCattleBreedOrganism: Coding = {
  code: '133260009',
  display: 'Red Polled Østland cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedSteppeCattleBreedOrganism: Coding = {
  code: '133261008',
  display: 'Red Steppe cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ReggianaCattleBreedOrganism: Coding = {
  code: '133262001',
  display: 'Reggiana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RetintaCattleBreedOrganism: Coding = {
  code: '133263006',
  display: 'Retinta cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomosinuanoCattleBreedOrganism: Coding = {
  code: '133264000',
  display: 'Romosinuano cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RussianBlackPiedCattleBreedOrganism: Coding = {
  code: '133265004',
  display: 'Russian Black Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RX3CattleBreedOrganism: Coding = {
  code: '133266003',
  display: 'RX3 cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SalornCattleBreedOrganism: Coding = {
  code: '133267007',
  display: 'Salorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MurbodenCattleBreedOrganism: Coding = {
  code: '133268002',
  display: 'Murboden cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SanMartineroCattleBreedOrganism: Coding = {
  code: '133269005',
  display: 'San Martinero cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SarabiCattleBreedOrganism: Coding = {
  code: '133270006',
  display: 'Sarabi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SharabiCattleBreedOrganism: Coding = {
  code: '133271005',
  display: 'Sharabi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShetlandCattleBreedOrganism: Coding = {
  code: '133272003',
  display: 'Shetland cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SimbrahCattleBreedOrganism: Coding = {
  code: '133273008',
  display: 'Simbrah cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthDevonCattleBreedOrganism: Coding = {
  code: '133274002',
  display: 'South Devon cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SuffolkCattleBreedOrganism: Coding = {
  code: '133275001',
  display: 'Suffolk cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SussexCattleBreedOrganism: Coding = {
  code: '133276000',
  display: 'Sussex cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwedishRedPolledCattleBreedOrganism: Coding = {
  code: '133277009',
  display: 'Swedish Red Polled cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TelemarkCattleBreedOrganism: Coding = {
  code: '133278004',
  display: 'Telemark cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TexasLonghornCattleBreedOrganism: Coding = {
  code: '133279007',
  display: 'Texas Longhorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TexonCattleBreedOrganism: Coding = {
  code: '133280005',
  display: 'Texon cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VestlandFjordCattleBreedOrganism: Coding = {
  code: '133281009',
  display: 'Vestland Fjord cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VestlandRedPolledCattleBreedOrganism: Coding = {
  code: '133282002',
  display: 'Vestland Red Polled cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WagyuCattleBreedOrganism: Coding = {
  code: '133283007',
  display: 'Wagyu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WhiteCceresCattleBreedOrganism: Coding = {
  code: '133284001',
  display: 'White Cáceres cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_XinjiangBrownCattleBreedOrganism: Coding = {
  code: '133285000',
  display: 'Xinjiang Brown cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YanbianCattleBreedOrganism: Coding = {
  code: '133286004',
  display: 'Yanbian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ZaobeiCattleBreedOrganism: Coding = {
  code: '133287008',
  display: 'Zaobei cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ZavotCattleBreedOrganism: Coding = {
  code: '133288003',
  display: 'Zavot cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ZnamenskCattleBreedOrganism: Coding = {
  code: '133289006',
  display: 'Znamensk cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlistanaSanabresaCattleBreedOrganism: Coding = {
  code: '133290002',
  display: 'Alistana-Sanabresa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AndalusianBlondCattleBreedOrganism: Coding = {
  code: '133291003',
  display: 'Andalusian Blond cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AostaBlackPiedCattleBreedOrganism: Coding = {
  code: '133292005',
  display: 'Aosta Black Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AostaChestnutCattleBreedOrganism: Coding = {
  code: '133293000',
  display: 'Aosta Chestnut cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AostaRedPiedCattleBreedOrganism: Coding = {
  code: '133294006',
  display: 'Aosta Red Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AracenaCattleBreedOrganism: Coding = {
  code: '133295007',
  display: 'Aracena cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArgentineFriesianCattleBreedOrganism: Coding = {
  code: '133296008',
  display: 'Argentine Friesian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArmoricanCattleBreedOrganism: Coding = {
  code: '133297004',
  display: 'Armorican cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArouquesaCattleBreedOrganism: Coding = {
  code: '133298009',
  display: 'Arouquesa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AureEtSaintGironsCattleBreedOrganism: Coding = {
  code: '133299001',
  display: 'Aure et Saint-Girons cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustralianWhiteCattleBreedOrganism: Coding = {
  code: '133300009',
  display: 'Australian White cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustrianSimmentalCattleBreedOrganism: Coding = {
  code: '133301008',
  display: 'Austrian Simmental cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustrianYellowCattleBreedOrganism: Coding = {
  code: '133302001',
  display: 'Austrian Yellow cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AvetonouCattleBreedOrganism: Coding = {
  code: '133303006',
  display: 'Avetonou cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AvilenaCattleBreedOrganism: Coding = {
  code: '133304000',
  display: 'Avilena cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AvilenaBlackIberianCattleBreedOrganism: Coding = {
  code: '133305004',
  display: 'Avilena-Black Iberian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BakosiCattleBreedOrganism: Coding = {
  code: '133306003',
  display: 'Bakosi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BakwiriCattleBreedOrganism: Coding = {
  code: '133307007',
  display: 'Bakwiri cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BalticBlackPiedCattleBreedOrganism: Coding = {
  code: '133308002',
  display: 'Baltic Black Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BaouleCattleBreedOrganism: Coding = {
  code: '133309005',
  display: 'Baoule cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BarrosaCattleBreedOrganism: Coding = {
  code: '133310000',
  display: 'Barrosa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BarrosoCattleBreedOrganism: Coding = {
  code: '133311001',
  display: 'Barroso cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BearnaisCattleBreedOrganism: Coding = {
  code: '133312008',
  display: 'Bearnais cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BeefShorthornCattleBreedOrganism: Coding = {
  code: '133313003',
  display: 'Beef shorthorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BeefSyntheticCattleBreedOrganism: Coding = {
  code: '133314009',
  display: 'Beef synthetic cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BeijingBlackPiedCattleBreedOrganism: Coding = {
  code: '133315005',
  display: 'Beijing Black Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BeiroaCattleBreedOrganism: Coding = {
  code: '133316006',
  display: 'Beiroa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelgianBlackPiedHolsteincattleBreedOrganism: Coding = {
  code: '133317002',
  display: 'Belgian Black Pied Holsteincattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelgianRedPiedCattleBreedOrganism: Coding = {
  code: '133318007',
  display: 'Belgian Red Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelgianWhiteAndRedCattleBreedOrganism: Coding = {
  code: '133319004',
  display: 'Belgian White and Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BeltedWelshCattleBreedOrganism: Coding = {
  code: '133320005',
  display: 'Belted Welsh cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BestuzhevCattleBreedOrganism: Coding = {
  code: '133321009',
  display: 'Bestuzhev cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BetizuakCattleBreedOrganism: Coding = {
  code: '133322002',
  display: 'Betizuak cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackBaldyCattleBreedOrganism: Coding = {
  code: '133323007',
  display: 'Black Baldy cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackForestCattleBreedOrganism: Coding = {
  code: '133324001',
  display: 'Black Forest cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackIberianCattleBreedOrganism: Coding = {
  code: '133325000',
  display: 'Black Iberian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorthernBlueCattleBreedOrganism: Coding = {
  code: '133326004',
  display: 'Northern Blue cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BragadoDoSorraiaCattleBreedOrganism: Coding = {
  code: '133327008',
  display: 'Bragado do Sorraia cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BragancaCattleBreedOrganism: Coding = {
  code: '133328003',
  display: 'Braganca cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrandroodLjsselveeCattleBreedOrganism: Coding = {
  code: '133329006',
  display: 'Brandrood ljsselvee cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrazilianPolledCattleBreedOrganism: Coding = {
  code: '133330001',
  display: 'Brazilian Polled cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BretonBlackPiedCattleBreedOrganism: Coding = {
  code: '133331002',
  display: 'Breton Black Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrownAtlasCattleBreedOrganism: Coding = {
  code: '133332009',
  display: 'Brown Atlas cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BulgarianBrownCattleBreedOrganism: Coding = {
  code: '133333004',
  display: 'Bulgarian Brown cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BulgarianRedCattleBreedOrganism: Coding = {
  code: '133334005',
  display: 'Bulgarian Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BurlinaCattleBreedOrganism: Coding = {
  code: '133335006',
  display: 'Burlina cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BurwashCattleBreedOrganism: Coding = {
  code: '133336007',
  display: 'Burwash cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ByelorussianRedCattleBreedOrganism: Coding = {
  code: '133337003',
  display: 'Byelorussian Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ByelorussianSyntheticCattleBreedOrganism: Coding = {
  code: '133338008',
  display: 'Byelorussian Synthetic cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CabanninaCattleBreedOrganism: Coding = {
  code: '133339000',
  display: 'Cabannina cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CaldeanoCattleBreedOrganism: Coding = {
  code: '133340003',
  display: 'Caldeano cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CaldelanaCattleBreedOrganism: Coding = {
  code: '133341004',
  display: 'Caldelana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CalvanaCattleBreedOrganism: Coding = {
  code: '133342006',
  display: 'Calvana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CamargueCattleBreedOrganism: Coding = {
  code: '133343001',
  display: 'Camargue cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CambodianCattleBreedOrganism: Coding = {
  code: '133344007',
  display: 'Cambodian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CaracuCattleBreedOrganism: Coding = {
  code: '133345008',
  display: 'Caracu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CarpathianBrownCattleBreedOrganism: Coding = {
  code: '133346009',
  display: 'Carpathian Brown cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CasanarenoCattleBreedOrganism: Coding = {
  code: '133347000',
  display: 'Casanareno cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CentralRussianBlackPiedCattleBreedOrganism: Coding = {
  code: '133348005',
  display: 'Central Russian Black Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChaouiaCattleBreedOrganism: Coding = {
  code: '133349002',
  display: 'Chaouia cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CharollandaisCattleBreedOrganism: Coding = {
  code: '133350002',
  display: 'Charollandais cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CharSwissCattleBreedOrganism: Coding = {
  code: '133351003',
  display: 'Char-swiss cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KoreanBlackCattleBreedOrganism: Coding = {
  code: '133352005',
  display: 'Korean Black cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChesiCattleBreedOrganism: Coding = {
  code: '133353000',
  display: 'Chesi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CheurfaCattleBreedOrganism: Coding = {
  code: '133354006',
  display: 'Cheurfa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChifordCattleBreedOrganism: Coding = {
  code: '133355007',
  display: 'Chiford cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChimaineCattleBreedOrganism: Coding = {
  code: '133356008',
  display: 'Chimaine cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChinampoCattleBreedOrganism: Coding = {
  code: '133357004',
  display: 'Chinampo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CildirCattleBreedOrganism: Coding = {
  code: '133358009',
  display: 'Cildir cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_COOPELSO93CattleBreedOrganism: Coding = {
  code: '133359001',
  display: 'COOPELSO 93 cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThraceCattleBreedOrganism: Coding = {
  code: '133360006',
  display: 'Thrace cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CorsicanCattleBreedOrganism: Coding = {
  code: '133361005',
  display: 'Corsican cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CretanLowlandCattleBreedOrganism: Coding = {
  code: '133362003',
  display: 'Cretan Lowland cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CretanMountainCattleBreedOrganism: Coding = {
  code: '133363008',
  display: 'Cretan Mountain cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CroatianRedCattleBreedOrganism: Coding = {
  code: '133364002',
  display: 'Croatian Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CukurovaCattleBreedOrganism: Coding = {
  code: '133365001',
  display: 'Cukurova cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CurraleiroCattleBreedOrganism: Coding = {
  code: '133366000',
  display: 'Curraleiro cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CyprusCattleBreedOrganism: Coding = {
  code: '133367009',
  display: 'Cyprus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CzechPiedCattleBreedOrganism: Coding = {
  code: '133368004',
  display: 'Czech Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DagestanMountainCattleBreedOrganism: Coding = {
  code: '133369007',
  display: 'Dagestan Mountain cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DairyShorthornCattleBreedOrganism: Coding = {
  code: '133370008',
  display: 'Dairy Shorthorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DairySyntheticCattleBreedOrganism: Coding = {
  code: '133371007',
  display: 'Dairy Synthetic cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanishRedPiedCattleBreedOrganism: Coding = {
  code: '133372000',
  display: 'Danish Red Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DengchuanCattleBreedOrganism: Coding = {
  code: '133373005',
  display: 'Dengchuan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DexterKerryCattleBreedOrganism: Coding = {
  code: '133374004',
  display: 'Dexter-Kerry cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DoranCattleBreedOrganism: Coding = {
  code: '133375003',
  display: 'Doran cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DornaCattleBreedOrganism: Coding = {
  code: '133376002',
  display: 'Dorna cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DortyolCattleBreedOrganism: Coding = {
  code: '133377006',
  display: 'Dortyol cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EastAnatolianRedCattleBreedOrganism: Coding = {
  code: '133378001',
  display: 'East Anatolian Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EastFinnishCattleBreedOrganism: Coding = {
  code: '133379009',
  display: 'East Finnish cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EastMacedonianCattleBreedOrganism: Coding = {
  code: '133380007',
  display: 'East Macedonian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EpirusCattleBreedOrganism: Coding = {
  code: '133381006',
  display: 'Epirus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EstonianBlackPiedCattleBreedOrganism: Coding = {
  code: '133382004',
  display: 'Estonian Black Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FerrandaisCattleBreedOrganism: Coding = {
  code: '133383009',
  display: 'Ferrandais cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FinnishAyrshireCattleBreedOrganism: Coding = {
  code: '133384003',
  display: 'Finnish Ayrshire cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FlemishCattleBreedOrganism: Coding = {
  code: '133385002',
  display: 'Flemish cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedFlemishCattleBreedOrganism: Coding = {
  code: '133386001',
  display: 'Red Flemish cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FortCrossCattleBreedOrganism: Coding = {
  code: '133387005',
  display: 'Fort Cross cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FratiCattleBreedOrganism: Coding = {
  code: '133388000',
  display: 'Frati cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EstonianNativeCattleBreedOrganism: Coding = {
  code: '133389008',
  display: 'Estonian Native cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FaeroesCattleBreedOrganism: Coding = {
  code: '133390004',
  display: 'Faeroes cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FrenchBrownCattleBreedOrganism: Coding = {
  code: '133391000',
  display: 'French Brown cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FrijolilloCattleBreedOrganism: Coding = {
  code: '133392007',
  display: 'Frijolillo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FRSCattleBreedOrganism: Coding = {
  code: '133393002',
  display: 'FRS cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GackoCattleBreedOrganism: Coding = {
  code: '133394008',
  display: 'Gacko cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GadoDaTerraCattleBreedOrganism: Coding = {
  code: '133395009',
  display: 'Gado da Terra cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GeorgianMountainCattleBreedOrganism: Coding = {
  code: '133396005',
  display: 'Georgian Mountain cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanBlackPiedCattleBreedOrganism: Coding = {
  code: '133397001',
  display: 'German Black Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanBlackPiedDairyCattleBreedOrganism: Coding = {
  code: '133398006',
  display: 'German Black Pied Dairy cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PechoraCattleBreedOrganism: Coding = {
  code: '133399003',
  display: 'Pechora cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PeeWeeCattleBreedOrganism: Coding = {
  code: '133400005',
  display: 'Pee Wee cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PeloponnesusCattleBreedOrganism: Coding = {
  code: '133401009',
  display: 'Peloponnesus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PesterCattleBreedOrganism: Coding = {
  code: '133402002',
  display: 'Pester cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PieRougeDeLQuoteEstCattleBreedOrganism: Coding = {
  code: '133403007',
  display: "Pie Rouge de l'Est cattle breed (organism)",
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PisanaCattleBreedOrganism: Coding = {
  code: '133404001',
  display: 'Pisana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanBrownCattleBreedOrganism: Coding = {
  code: '133405000',
  display: 'German Brown cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanShorthornCattleBreedOrganism: Coding = {
  code: '133406004',
  display: 'German Shorthorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GhanaShorthornCattleBreedOrganism: Coding = {
  code: '133407008',
  display: 'Ghana Shorthorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GlanDonnersbergCattleBreedOrganism: Coding = {
  code: '133408003',
  display: 'Glan-Donnersberg cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GoleCattleBreedOrganism: Coding = {
  code: '133409006',
  display: 'Gole cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GolpayeganiCattleBreedOrganism: Coding = {
  code: '133410001',
  display: 'Golpayegani cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GorbatovRedCattleBreedOrganism: Coding = {
  code: '133411002',
  display: 'Gorbatov Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GorynCattleBreedOrganism: Coding = {
  code: '133412009',
  display: 'Goryn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GreaterCaucasusCattleBreedOrganism: Coding = {
  code: '133413004',
  display: 'Greater Caucasus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolishBlackAndWhiteLowlandCattleBreedOrganism: Coding = {
  code: '133414005',
  display: 'Polish Black and White Lowland cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolishSimmentalCattleBreedOrganism: Coding = {
  code: '133415006',
  display: 'Polish Simmental cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolledJerseyCattleBreedOrganism: Coding = {
  code: '133416007',
  display: 'Polled Jersey cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolledLincolnRedCattleBreedOrganism: Coding = {
  code: '133417003',
  display: 'Polled Lincoln Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolledShorthornUnitedStatesOfAmericaCattleBreedOrganism: Coding = {
  code: '133418008',
  display: 'Polled Shorthorn (United States of America) cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolledSimmentalCattleBreedOrganism: Coding = {
  code: '133419000',
  display: 'Polled Simmental cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GreekShorthornCattleBreedOrganism: Coding = {
  code: '133420006',
  display: 'Greek Shorthorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GreekSteppeCattleBreedOrganism: Coding = {
  code: '133421005',
  display: 'Greek Steppe cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GrayAlpineCattleBreedOrganism: Coding = {
  code: '133422003',
  display: 'Gray Alpine cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuadianaSpottedCattleBreedOrganism: Coding = {
  code: '133423008',
  display: 'Guadiana Spotted cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuelmaCattleBreedOrganism: Coding = {
  code: '133424002',
  display: 'Guelma cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HarzRedCattleBreedOrganism: Coding = {
  code: '133425001',
  display: 'Harz Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HawaiianWildCattleBreedOrganism: Coding = {
  code: '133426000',
  display: 'Hawaiian wild cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HerelandCattleBreedOrganism: Coding = {
  code: '133427009',
  display: 'Hereland cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HolgusCattleBreedOrganism: Coding = {
  code: '133428004',
  display: 'Holgus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HrbineckyCattleBreedOrganism: Coding = {
  code: '133429007',
  display: 'Hrbinecky cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolledSussexCattleBreedOrganism: Coding = {
  code: '133430002',
  display: 'Polled Sussex cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolledWelshBlackCattleBreedOrganism: Coding = {
  code: '133431003',
  display: 'Polled Welsh Black cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PontremoleseCattleBreedOrganism: Coding = {
  code: '133432005',
  display: 'Pontremolese cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PretaCattleBreedOrganism: Coding = {
  code: '133433000',
  display: 'Preta cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PuertoRicanCriolloCattleBreedOrganism: Coding = {
  code: '133434006',
  display: 'Puerto Rican Criollo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PyreneanCattleBreedOrganism: Coding = {
  code: '133435007',
  display: 'Pyrenean cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HuertanaCattleBreedOrganism: Coding = {
  code: '133436008',
  display: 'Huertana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HungarianPiedCattleBreedOrganism: Coding = {
  code: '133437004',
  display: 'Hungarian Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HungarofriesCattleBreedOrganism: Coding = {
  code: '133438009',
  display: 'Hungarofries cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ImprovedRodopiCattleBreedOrganism: Coding = {
  code: '133439001',
  display: 'Improved Rodopi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_INRA95CattleBreedOrganism: Coding = {
  code: '133440004',
  display: 'INRA 95 cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ItalianBrownCattleBreedOrganism: Coding = {
  code: '133441000',
  display: 'Italian Brown cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ItalianRedPiedCattleBreedOrganism: Coding = {
  code: '133442007',
  display: 'Italian Red Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JapaneseBlackCattleBreedOrganism: Coding = {
  code: '133443002',
  display: 'Japanese Black cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JapaneseBrownCattleBreedOrganism: Coding = {
  code: '133444008',
  display: 'Japanese Brown cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JapanesePollCattleBreedOrganism: Coding = {
  code: '133445009',
  display: 'Japanese Poll cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_QinchuanCattleBreedOrganism: Coding = {
  code: '133446005',
  display: 'Qinchuan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RamoGrandeCattleBreedOrganism: Coding = {
  code: '133447001',
  display: 'Ramo Grande cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RandallLinebackCattleBreedOrganism: Coding = {
  code: '133448006',
  display: 'Randall Lineback cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedGallowayCattleBreedOrganism: Coding = {
  code: '133449003',
  display: 'Red Galloway cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RegusCattleBreedOrganism: Coding = {
  code: '133450003',
  display: 'Regus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RendenaCattleBreedOrganism: Coding = {
  code: '133451004',
  display: 'Rendena cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JapaneseShorthornCattleBreedOrganism: Coding = {
  code: '133452006',
  display: 'Japanese Shorthorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JarmelistaCattleBreedOrganism: Coding = {
  code: '133453001',
  display: 'Jarmelista cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KabyleCattleBreedOrganism: Coding = {
  code: '133454007',
  display: 'Kabyle cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KapsikiCattleBreedOrganism: Coding = {
  code: '133455008',
  display: 'Kapsiki cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KateriniCattleBreedOrganism: Coding = {
  code: '133456009',
  display: 'Katerini cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KenranCattleBreedOrganism: Coding = {
  code: '133457000',
  display: 'Kenran cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KhevsurianCattleBreedOrganism: Coding = {
  code: '133458005',
  display: 'Khevsurian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KilisCattleBreedOrganism: Coding = {
  code: '133459002',
  display: 'Kilis cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KochiCattleBreedOrganism: Coding = {
  code: '133460007',
  display: 'Kochi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KoreanNativeCattleBreedOrganism: Coding = {
  code: '133461006',
  display: 'Korean Native cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RhaetianGrayCattleBreedOrganism: Coding = {
  code: '133462004',
  display: 'Rhaetian Gray cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RioLimonDairyCriolloCattleBreedOrganism: Coding = {
  code: '133463009',
  display: 'Rio Limon Dairy Criollo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RodopiCattleBreedOrganism: Coding = {
  code: '133464003',
  display: 'Rodopi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomanianRedCattleBreedOrganism: Coding = {
  code: '133465002',
  display: 'Romanian Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomanianBrownCattleBreedOrganism: Coding = {
  code: '133466001',
  display: 'Romanian Brown cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RussianBrownCattleBreedOrganism: Coding = {
  code: '133467005',
  display: 'Russian Brown cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KostromaCattleBreedOrganism: Coding = {
  code: '133468000',
  display: 'Kostroma cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KravarskyCattleBreedOrganism: Coding = {
  code: '133469008',
  display: 'Kravarsky cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KuchinoshimaCattleBreedOrganism: Coding = {
  code: '133470009',
  display: 'Kuchinoshima cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MurrayGrayCattleBreedOrganism: Coding = {
  code: '133471008',
  display: 'Murray Gray cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustralianShorthornCattleBreedOrganism: Coding = {
  code: '133472001',
  display: 'Australian Shorthorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KumamotoCattleBreedOrganism: Coding = {
  code: '133473006',
  display: 'Kumamoto cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LaguneCattleBreedOrganism: Coding = {
  code: '133474000',
  display: 'Lagune cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LakenvelderCattleBreedOrganism: Coding = {
  code: '133475004',
  display: 'Lakenvelder cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LatvianBlueRoanCattleBreedOrganism: Coding = {
  code: '133476003',
  display: 'Latvian Blue Roan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LaVelasquezCattleBreedOrganism: Coding = {
  code: '133477007',
  display: 'La Velasquez cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SardinianCattleBreedOrganism: Coding = {
  code: '133478002',
  display: 'Sardinian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SardinianBrownCattleBreedOrganism: Coding = {
  code: '133479005',
  display: 'Sardinian brown cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SavinjaGrayCattleBreedOrganism: Coding = {
  code: '133480008',
  display: 'Savinja Gray cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SayaguesaCattleBreedOrganism: Coding = {
  code: '133481007',
  display: 'Sayaguesa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SeferihisarCattleBreedOrganism: Coding = {
  code: '133482000',
  display: 'Seferihisar cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShkodraRedCattleBreedOrganism: Coding = {
  code: '133483005',
  display: 'Shkodra Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LebaneseCattleBreedOrganism: Coding = {
  code: '133484004',
  display: 'Lebanese cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LebedinCattleBreedOrganism: Coding = {
  code: '133485003',
  display: 'Lebedin cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LesserCaucasusCattleBreedOrganism: Coding = {
  code: '133486002',
  display: 'Lesser Caucasus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LiberianDwarfCattleBreedOrganism: Coding = {
  code: '133487006',
  display: 'Liberian Dwarf cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LibyanCattleBreedOrganism: Coding = {
  code: '133488001',
  display: 'Libyan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LimCattleBreedOrganism: Coding = {
  code: '133489009',
  display: 'Lim cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LimianaCattleBreedOrganism: Coding = {
  code: '133490000',
  display: 'Limiana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LimpurgerCattleBreedOrganism: Coding = {
  code: '133491001',
  display: 'Limpurger cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LobiCattleBreedOrganism: Coding = {
  code: '133492008',
  display: 'Lobi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LourdaisCattleBreedOrganism: Coding = {
  code: '133493003',
  display: 'Lourdais cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SlovakianPiedCattleBreedOrganism: Coding = {
  code: '133494009',
  display: 'Slovakian Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SlovakianPinzgauCattleBreedOrganism: Coding = {
  code: '133495005',
  display: 'Slovakian Pinzgau cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SlovenianBrownCattleBreedOrganism: Coding = {
  code: '133496006',
  display: 'Slovenian Brown cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SombaCattleBreedOrganism: Coding = {
  code: '133497002',
  display: 'Somba cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthAfricanBrownSwissCattleBreedOrganism: Coding = {
  code: '133498007',
  display: 'South African Brown Swiss cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthAnatolianRedCattleBreedOrganism: Coding = {
  code: '133499004',
  display: 'South Anatolian Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LucernaCattleBreedOrganism: Coding = {
  code: '133500008',
  display: 'Lucerna cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LuxiCattleBreedOrganism: Coding = {
  code: '133501007',
  display: 'Luxi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MacedonianBusaCattleBreedOrganism: Coding = {
  code: '133502000',
  display: 'Macedonian Busa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MakaweliCattleBreedOrganism: Coding = {
  code: '133503005',
  display: 'Makaweli cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MarinhoaCattleBreedOrganism: Coding = {
  code: '133504004',
  display: 'Marinhoa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MaronesaCattleBreedOrganism: Coding = {
  code: '133505003',
  display: 'Maronesa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MazuryCattleBreedOrganism: Coding = {
  code: '133506002',
  display: 'Mazury cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MessaoriaCattleBreedOrganism: Coding = {
  code: '133507006',
  display: 'Messaoria cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MetohijaRedCattleBreedOrganism: Coding = {
  code: '133508001',
  display: 'Metohija Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MingrelianRedCattleBreedOrganism: Coding = {
  code: '133509009',
  display: 'Mingrelian Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthernUkrainianCattleBreedOrganism: Coding = {
  code: '133510004',
  display: 'Southern Ukrainian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SpanishBrownAlpineCattleBreedOrganism: Coding = {
  code: '133511000',
  display: 'Spanish Brown Alpine cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SuksunCattleBreedOrganism: Coding = {
  code: '133512007',
  display: 'Suksun cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwissBlackPiedCattleBreedOrganism: Coding = {
  code: '133513002',
  display: 'Swiss Black Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SychevkaCattleBreedOrganism: Coding = {
  code: '133514008',
  display: 'Sychevka cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SykiaCattleBreedOrganism: Coding = {
  code: '133515009',
  display: 'Sykia cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MinhotaCattleBreedOrganism: Coding = {
  code: '133516005',
  display: 'Minhota cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MinorcanCattleBreedOrganism: Coding = {
  code: '133517001',
  display: 'Minorcan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MishimaCattleBreedOrganism: Coding = {
  code: '133518006',
  display: 'Mishima cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ModeneseCattleBreedOrganism: Coding = {
  code: '133519003',
  display: 'Modenese cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MonchinaCattleBreedOrganism: Coding = {
  code: '133520009',
  display: 'Monchina cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MontafonCattleBreedOrganism: Coding = {
  code: '133521008',
  display: 'Montafon cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MontbeliardCattleBreedOrganism: Coding = {
  code: '133522001',
  display: 'Montbeliard cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MorenasDelNoroesteCattleBreedOrganism: Coding = {
  code: '133523006',
  display: 'Morenas del Noroeste cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MurcianCattleBreedOrganism: Coding = {
  code: '133524000',
  display: 'Murcian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MurnauWerdenfelsCattleBreedOrganism: Coding = {
  code: '133525004',
  display: 'Murnau-Werdenfels cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TagilCattleBreedOrganism: Coding = {
  code: '133526003',
  display: 'Tagil cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TajmaCattleBreedOrganism: Coding = {
  code: '133527007',
  display: 'Tajma cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TambovRedCattleBreedOrganism: Coding = {
  code: '133528002',
  display: 'Tambov Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TarinaCattleBreedOrganism: Coding = {
  code: '133529005',
  display: 'Tarina cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThessalyCattleBreedOrganism: Coding = {
  code: '133530000',
  display: 'Thessaly cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TinimaCattleBreedOrganism: Coding = {
  code: '133531001',
  display: 'Tinima cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NantaisCattleBreedOrganism: Coding = {
  code: '133532008',
  display: 'Nantais cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NejdiCattleBreedOrganism: Coding = {
  code: '133533003',
  display: 'Nejdi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NQuoteGabouCattleBreedOrganism: Coding = {
  code: '133534009',
  display: "N'Gabou cattle breed (organism)",
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorthFinncattleCattleBreedOrganism: Coding = {
  code: '133535005',
  display: 'North Finncattle cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OropaCattleBreedOrganism: Coding = {
  code: '133536006',
  display: 'Oropa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OulmesBlondCattleBreedOrganism: Coding = {
  code: '133537002',
  display: 'Oulmes Blond cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PajunaCattleBreedOrganism: Coding = {
  code: '133538007',
  display: 'Pajuna cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PalmeraCattleBreedOrganism: Coding = {
  code: '133539004',
  display: 'Palmera cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PankotaRedCattleBreedOrganism: Coding = {
  code: '133540002',
  display: 'Pankota Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PaphosCattleBreedOrganism: Coding = {
  code: '133541003',
  display: 'Paphos cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TinosCattleBreedOrganism: Coding = {
  code: '133542005',
  display: 'Tinos cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TransylvanianPinzgauCattleBreedOrganism: Coding = {
  code: '133543000',
  display: 'Transylvanian Pinzgau cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TropicalDairyCattleCattleBreedOrganism: Coding = {
  code: '133544006',
  display: 'Tropical Dairy Cattle cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TropicanaCattleBreedOrganism: Coding = {
  code: '133545007',
  display: 'Tropicana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TudancaCattleBreedOrganism: Coding = {
  code: '133546008',
  display: 'Tudanca cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TurinoCattleBreedOrganism: Coding = {
  code: '133547004',
  display: 'Turino cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TurkishBrownCattleBreedOrganism: Coding = {
  code: '133548009',
  display: 'Turkish Brown cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TuxZillertalCattleBreedOrganism: Coding = {
  code: '133549001',
  display: 'Tux-Zillertal cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TyrolGrayCattleBreedOrganism: Coding = {
  code: '133550001',
  display: 'Tyrol Gray cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AbondanceCattleBreedOrganism: Coding = {
  code: '133551002',
  display: 'Abondance cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlaTauCattleBreedOrganism: Coding = {
  code: '133552009',
  display: 'Ala-Tau cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlbanianIllyrianCattleBreedOrganism: Coding = {
  code: '133553004',
  display: 'Albanian Illyrian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlbanianDwarfCattleBreedOrganism: Coding = {
  code: '133554005',
  display: 'Albanian Dwarf cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_UkrainianWhiteheadedCattleBreedOrganism: Coding = {
  code: '133555006',
  display: 'Ukrainian Whiteheaded cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_UralBlackPiedCattleBreedOrganism: Coding = {
  code: '133556007',
  display: 'Ural Black Pied cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ValdresCattleBreedOrganism: Coding = {
  code: '133557003',
  display: 'Valdres cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VaynolCattleBreedOrganism: Coding = {
  code: '133558008',
  display: 'Vaynol cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VerinesaCattleBreedOrganism: Coding = {
  code: '133559000',
  display: 'Verinesa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VianesaCattleBreedOrganism: Coding = {
  code: '133560005',
  display: 'Vianesa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VillardDeLansCattleBreedOrganism: Coding = {
  code: '133561009',
  display: 'Villard-de-Lans cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VogelsbergCattleBreedOrganism: Coding = {
  code: '133562002',
  display: 'Vogelsberg cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PieRougeDesPlainesCattleBreedOrganism: Coding = {
  code: '133563007',
  display: 'Pie Rouge des Plaines cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VorderwaldCattleBreedOrganism: Coding = {
  code: '133564001',
  display: 'Vorderwald cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WestAfricanDwarfShorthornCattleBreedOrganism: Coding = {
  code: '133565000',
  display: 'West African Dwarf Shorthorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WestFinnishCattleBreedOrganism: Coding = {
  code: '133566004',
  display: 'West Finnish cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WestMacedonianCattleBreedOrganism: Coding = {
  code: '133567008',
  display: 'West Macedonian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WhitebredShorthornCattleBreedOrganism: Coding = {
  code: '133568003',
  display: 'Whitebred Shorthorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WhiteGallowayCattleBreedOrganism: Coding = {
  code: '133569006',
  display: 'White Galloway cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WhiteWelshCattleBreedOrganism: Coding = {
  code: '133570007',
  display: 'White Welsh cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WitrikCattleBreedOrganism: Coding = {
  code: '133571006',
  display: 'Witrik cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YacumentoCattleBreedOrganism: Coding = {
  code: '133572004',
  display: 'Yacumento cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YaroslavlCattleBreedOrganism: Coding = {
  code: '133573009',
  display: 'Yaroslavl cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YurinoCattleBreedOrganism: Coding = {
  code: '133574003',
  display: 'Yurino cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AleppoCattleBreedOrganism: Coding = {
  code: '133575002',
  display: 'Aleppo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SchwyzCattleBreedOrganism: Coding = {
  code: '133576001',
  display: 'Schwyz cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BusaCattleBreedOrganism: Coding = {
  code: '133577005',
  display: 'Busa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChiangusCattleBreedOrganism: Coding = {
  code: '133578000',
  display: 'Chiangus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HallingdalCattleBreedOrganism: Coding = {
  code: '133579008',
  display: 'Hallingdal cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanishJerseyCattleBreedOrganism: Coding = {
  code: '133580006',
  display: 'Danish Jersey cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EnderbyIslandCattleBreedOrganism: Coding = {
  code: '133581005',
  display: 'Enderby Island cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanAngusCattleBreedOrganism: Coding = {
  code: '133582003',
  display: 'German Angus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IsraeliRedCattleBreedOrganism: Coding = {
  code: '133583008',
  display: 'Israeli Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LinebackCattleBreedOrganism: Coding = {
  code: '133584002',
  display: 'Lineback cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MertolengaCattleBreedOrganism: Coding = {
  code: '133585001',
  display: 'Mertolenga cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedFriesianCattleBreedOrganism: Coding = {
  code: '133586000',
  display: 'Red Friesian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SenegusCattleBreedOrganism: Coding = {
  code: '133587009',
  display: 'Senegus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthernCriouloCattleBreedOrganism: Coding = {
  code: '133588004',
  display: 'Southern Crioulo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VosgesCattleBreedOrganism: Coding = {
  code: '133589007',
  display: 'Vosges cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MontanaraCattleBreedOrganism: Coding = {
  code: '133590003',
  display: 'Montanara cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlmanzorenaCattleBreedOrganism: Coding = {
  code: '133591004',
  display: 'Almanzorena cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LorquinaCattleBreedOrganism: Coding = {
  code: '133592006',
  display: 'Lorquina cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CalasparrenaCattleBreedOrganism: Coding = {
  code: '133593001',
  display: 'Calasparrena cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmritmahalCattleBreedOrganism: Coding = {
  code: '133594007',
  display: 'Amritmahal cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BachaurCattleBreedOrganism: Coding = {
  code: '133595008',
  display: 'Bachaur cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BarkaCattleBreedOrganism: Coding = {
  code: '133596009',
  display: 'Barka cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BengaliCattleBreedOrganism: Coding = {
  code: '133597000',
  display: 'Bengali cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BhagnariCattleBreedOrganism: Coding = {
  code: '133598005',
  display: 'Bhagnari cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoranCattleBreedOrganism: Coding = {
  code: '133599002',
  display: 'Boran cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChanniCattleBreedOrganism: Coding = {
  code: '133600004',
  display: 'Channi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CholistaniCattleBreedOrganism: Coding = {
  code: '133601000',
  display: 'Cholistani cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DajalCattleBreedOrganism: Coding = {
  code: '133602007',
  display: 'Dajal cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DangiCattleBreedOrganism: Coding = {
  code: '133603002',
  display: 'Dangi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DeoniCattleBreedOrganism: Coding = {
  code: '133604008',
  display: 'Deoni cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DhanniCattleBreedOrganism: Coding = {
  code: '133605009',
  display: 'Dhanni cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GaolaoCattleBreedOrganism: Coding = {
  code: '133606005',
  display: 'Gaolao cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HallikarCattleBreedOrganism: Coding = {
  code: '133607001',
  display: 'Hallikar cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HarianaCattleBreedOrganism: Coding = {
  code: '133608006',
  display: 'Hariana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IndoBrazilianCattleBreedOrganism: Coding = {
  code: '133609003',
  display: 'Indo-Brazilian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KangayamCattleBreedOrganism: Coding = {
  code: '133610008',
  display: 'Kangayam cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KankrejCattleBreedOrganism: Coding = {
  code: '133611007',
  display: 'Kankrej cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KenkathaCattleBreedOrganism: Coding = {
  code: '133612000',
  display: 'Kenkatha cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KherigarhCattleBreedOrganism: Coding = {
  code: '133613005',
  display: 'Kherigarh cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KhillariCattleBreedOrganism: Coding = {
  code: '133614004',
  display: 'Khillari cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KrishnaValleyCattleBreedOrganism: Coding = {
  code: '133615003',
  display: 'Krishna Valley cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LohaniCattleBreedOrganism: Coding = {
  code: '133616002',
  display: 'Lohani cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MalviCattleBreedOrganism: Coding = {
  code: '133617006',
  display: 'Malvi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MewatiCattleBreedOrganism: Coding = {
  code: '133618001',
  display: 'Mewati cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NagoriCattleBreedOrganism: Coding = {
  code: '133619009',
  display: 'Nagori cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NeloreCattleBreedOrganism: Coding = {
  code: '133620003',
  display: 'Nelore cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NimariCattleBreedOrganism: Coding = {
  code: '133621004',
  display: 'Nimari cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PonwarCattleBreedOrganism: Coding = {
  code: '133622006',
  display: 'Ponwar cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RathCattleBreedOrganism: Coding = {
  code: '133623001',
  display: 'Rath cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RathiCattleBreedOrganism: Coding = {
  code: '133624007',
  display: 'Rathi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedSindhiCattleBreedOrganism: Coding = {
  code: '133625008',
  display: 'Red Sindhi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RojhanCattleBreedOrganism: Coding = {
  code: '133626009',
  display: 'Rojhan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SahiwalCattleBreedOrganism: Coding = {
  code: '133627000',
  display: 'Sahiwal cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SiriZebuCattleBreedOrganism: Coding = {
  code: '133628005',
  display: 'Siri zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TharparkarCattleBreedOrganism: Coding = {
  code: '133629002',
  display: 'Tharparkar cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ZanzibarZebuCattleBreedOrganism: Coding = {
  code: '133630007',
  display: 'Zanzibar Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArsiCattleBreedOrganism: Coding = {
  code: '133631006',
  display: 'Arsi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AtpadiMahalCattleBreedOrganism: Coding = {
  code: '133632004',
  display: 'Atpadi Mahal cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AzaouakCattleBreedOrganism: Coding = {
  code: '133633009',
  display: 'Azaouak cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AzerbaijanZebuCattleBreedOrganism: Coding = {
  code: '133634003',
  display: 'Azerbaijan Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BaggaraCattleBreedOrganism: Coding = {
  code: '133635002',
  display: 'Baggara cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BambawaCattleBreedOrganism: Coding = {
  code: '133636001',
  display: 'Bambawa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BamiCattleBreedOrganism: Coding = {
  code: '133637005',
  display: 'Bami cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BanyoCattleBreedOrganism: Coding = {
  code: '133638000',
  display: 'Banyo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BargurCattleBreedOrganism: Coding = {
  code: '133639008',
  display: 'Bargur cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BariCattleBreedOrganism: Coding = {
  code: '133640005',
  display: 'Bari cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BimalCattleBreedOrganism: Coding = {
  code: '133641009',
  display: 'Bimal cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BorneoZebuCattleBreedOrganism: Coding = {
  code: '133642002',
  display: 'Borneo Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ButanaCattleBreedOrganism: Coding = {
  code: '133643007',
  display: 'Butana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChittagongRedCattleBreedOrganism: Coding = {
  code: '133644001',
  display: 'Chittagong Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CutchiCattleBreedOrganism: Coding = {
  code: '133645000',
  display: 'Cutchi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DairyZebuOfUberabaCattleBreedOrganism: Coding = {
  code: '133646004',
  display: 'Dairy Zebu of Uberaba cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DashtiariCattleBreedOrganism: Coding = {
  code: '133647008',
  display: 'Dashtiari cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DialiCattleBreedOrganism: Coding = {
  code: '133648003',
  display: 'Diali cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DidingaCattleBreedOrganism: Coding = {
  code: '133649006',
  display: 'Didinga cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DongolaCattleBreedOrganism: Coding = {
  code: '133650006',
  display: 'Dongola cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FellataCattleBreedOrganism: Coding = {
  code: '133651005',
  display: 'Fellata cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TurkmenZebuCattleBreedOrganism: Coding = {
  code: '133652003',
  display: 'Turkmen zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AbyssinianHighlandZebuCattleBreedOrganism: Coding = {
  code: '133653008',
  display: 'Abyssinian Highland Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AbyssinianShorthornedZebuCattleBreedOrganism: Coding = {
  code: '133654002',
  display: 'Abyssinian Shorthorned Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AcehCattleBreedOrganism: Coding = {
  code: '133655001',
  display: 'Aceh cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AchhamCattleBreedOrganism: Coding = {
  code: '133656000',
  display: 'Achham cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GarreCattleBreedOrganism: Coding = {
  code: '133657009',
  display: 'Garre cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GasaraCattleBreedOrganism: Coding = {
  code: '133658004',
  display: 'Gasara cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GobraCattleBreedOrganism: Coding = {
  code: '133659007',
  display: 'Gobra cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GoomsurCattleBreedOrganism: Coding = {
  code: '133660002',
  display: 'Goomsur cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GujamavuCattleBreedOrganism: Coding = {
  code: '133661003',
  display: 'Gujamavu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LeiqiongCattleBreedOrganism: Coding = {
  code: '133662005',
  display: 'Leiqiong cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HissarCattleBreedOrganism: Coding = {
  code: '133663000',
  display: 'Hissar cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IngessanaCattleBreedOrganism: Coding = {
  code: '133664006',
  display: 'Ingessana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JamaicaBrahmanCattleBreedOrganism: Coding = {
  code: '133665007',
  display: 'Jamaica Brahman cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JellicutCattleBreedOrganism: Coding = {
  code: '133666008',
  display: 'Jellicut cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AdamawaCattleBreedOrganism: Coding = {
  code: '133667004',
  display: 'Adamawa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AdenZebuCattleBreedOrganism: Coding = {
  code: '133668009',
  display: 'Aden Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AfghanCattleBreedOrganism: Coding = {
  code: '133669001',
  display: 'Afghan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlambadiCattleBreedOrganism: Coding = {
  code: '133670000',
  display: 'Alambadi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_UmblacheryCattleBreedOrganism: Coding = {
  code: '133671001',
  display: 'Umblachery cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VenezuelanZebuCattleBreedOrganism: Coding = {
  code: '133672008',
  display: 'Venezuelan Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PantaneiroCattleBreedOrganism: Coding = {
  code: '133673003',
  display: 'Pantaneiro cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JenubiCattleBreedOrganism: Coding = {
  code: '133674009',
  display: 'Jenubi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JidduCattleBreedOrganism: Coding = {
  code: '133675005',
  display: 'Jiddu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JijjigaZebuCattleBreedOrganism: Coding = {
  code: '133676006',
  display: 'Jijjiga Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KabotaCattleBreedOrganism: Coding = {
  code: '133677002',
  display: 'Kabota cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KachchaSiriCattleBreedOrganism: Coding = {
  code: '133678007',
  display: 'Kachcha Siri cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KalakheriCattleBreedOrganism: Coding = {
  code: '133679004',
  display: 'Kalakheri cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KamdhinoCattleBreedOrganism: Coding = {
  code: '133680001',
  display: 'Kamdhino cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KandahariCattleBreedOrganism: Coding = {
  code: '133681002',
  display: 'Kandahari cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KaninganCattleBreedOrganism: Coding = {
  code: '133682009',
  display: 'Kaningan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WakwaCattleBreedOrganism: Coding = {
  code: '133683004',
  display: 'Wakwa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WhiteFulaniCattleBreedOrganism: Coding = {
  code: '133684005',
  display: 'White Fulani cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YemeniZebuCattleBreedOrganism: Coding = {
  code: '133685006',
  display: 'Yemeni Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IranianZebuCattleBreedOrganism: Coding = {
  code: '133686007',
  display: 'Iranian Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KhorsanCattleBreedOrganism: Coding = {
  code: '133687003',
  display: 'Khorsan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolledGirCattleBreedOrganism: Coding = {
  code: '133688008',
  display: 'Polled Gir cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KappiliyanCattleBreedOrganism: Coding = {
  code: '133689000',
  display: 'Kappiliyan cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KaramajongCattleBreedOrganism: Coding = {
  code: '133690009',
  display: 'Karamajong cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KenanaCattleBreedOrganism: Coding = {
  code: '133691008',
  display: 'Kenana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KenyaBoranCattleBreedOrganism: Coding = {
  code: '133692001',
  display: 'Kenya Boran cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KenyaZebuCattleBreedOrganism: Coding = {
  code: '133693006',
  display: 'Kenya Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KhamalaCattleBreedOrganism: Coding = {
  code: '133694000',
  display: 'Khamala cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KhurasaniZebuCattleBreedOrganism: Coding = {
  code: '133695004',
  display: 'Khurasani zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KilaraCattleBreedOrganism: Coding = {
  code: '133696003',
  display: 'Kilara cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KinniyaCattleBreedOrganism: Coding = {
  code: '133697007',
  display: 'Kinniya cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KonariCattleBreedOrganism: Coding = {
  code: '133698002',
  display: 'Konari cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuzeratCattleBreedOrganism: Coding = {
  code: '133699005',
  display: 'Guzerat cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TadzhikZebuCattleBreedOrganism: Coding = {
  code: '133700006',
  display: 'Tadzhik zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DeogirCattleBreedOrganism: Coding = {
  code: '133701005',
  display: 'Deogir cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GayalCattleBreedOrganism: Coding = {
  code: '133702003',
  display: 'Gayal cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanBisonXCattleBreedOrganism: Coding = {
  code: '133703008',
  display: 'American bison X cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustralianBrafordCattleBreedOrganism: Coding = {
  code: '133704002',
  display: 'Australian Braford cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KrishnagariCattleBreedOrganism: Coding = {
  code: '133705001',
  display: 'Krishnagari cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KumauniCattleBreedOrganism: Coding = {
  code: '133706000',
  display: 'Kumauni cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LadakhiCattleBreedOrganism: Coding = {
  code: '133707009',
  display: 'Ladakhi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LatukaCattleBreedOrganism: Coding = {
  code: '133708004',
  display: 'Latuka cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LugwareCattleBreedOrganism: Coding = {
  code: '133709007',
  display: 'Lugware cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MadagascarZebuCattleBreedOrganism: Coding = {
  code: '133710002',
  display: 'Madagascar Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MadaripurCattleBreedOrganism: Coding = {
  code: '133711003',
  display: 'Madaripur cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MagalCattleBreedOrganism: Coding = {
  code: '133712005',
  display: 'Magal cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MalawiZebuCattleBreedOrganism: Coding = {
  code: '133713000',
  display: 'Malawi Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MalnadGiddaCattleBreedOrganism: Coding = {
  code: '133714006',
  display: 'Malnad Gidda cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustralianFriesianSahiwalCattleBreedOrganism: Coding = {
  code: '133715007',
  display: 'Australian Friesian Sahiwal cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrafordCattleBreedOrganism: Coding = {
  code: '133716008',
  display: 'Braford cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrahmousinCattleBreedOrganism: Coding = {
  code: '133717004',
  display: 'Brahmousin cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanchimCattleBreedOrganism: Coding = {
  code: '133718009',
  display: 'Canchim cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CharbrayCattleBreedOrganism: Coding = {
  code: '133719001',
  display: 'Charbray cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DroughtmasterCattleBreedOrganism: Coding = {
  code: '133720007',
  display: 'Droughtmaster cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MampatiCattleBreedOrganism: Coding = {
  code: '133721006',
  display: 'Mampati cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ManapariCattleBreedOrganism: Coding = {
  code: '133722004',
  display: 'Manapari cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MaureCattleBreedOrganism: Coding = {
  code: '133723009',
  display: 'Maure cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MazandaraniCattleBreedOrganism: Coding = {
  code: '133724003',
  display: 'Mazandarani cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MeraukeCattleBreedOrganism: Coding = {
  code: '133725002',
  display: 'Merauke cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MhaswadCattleBreedOrganism: Coding = {
  code: '133727005',
  display: 'Mhaswad cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MiniatureZebuCattleBreedOrganism: Coding = {
  code: '133728000',
  display: 'Miniature Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MongallaCattleBreedOrganism: Coding = {
  code: '133729008',
  display: 'Mongalla cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MorangCattleBreedOrganism: Coding = {
  code: '133730003',
  display: 'Morang cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GelbrayCattleBreedOrganism: Coding = {
  code: '133731004',
  display: 'Gelbray cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JamaicaBlackCattleBreedOrganism: Coding = {
  code: '133732006',
  display: 'Jamaica Black cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JamaicaHopeCattleBreedOrganism: Coding = {
  code: '133733001',
  display: 'Jamaica Hope cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JamaicaRedCattleBreedOrganism: Coding = {
  code: '133734007',
  display: 'Jamaica Red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KaranFriesCattleBreedOrganism: Coding = {
  code: '133735008',
  display: 'Karan Fries cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KaranSwissCattleBreedOrganism: Coding = {
  code: '133736009',
  display: 'Karan Swiss cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MozambiqueAngoniCattleBreedOrganism: Coding = {
  code: '133737000',
  display: 'Mozambique Angoni cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MpwapwaCattleBreedOrganism: Coding = {
  code: '133738005',
  display: 'Mpwapwa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MurleCattleBreedOrganism: Coding = {
  code: '133739002',
  display: 'Murle cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NakaliCattleBreedOrganism: Coding = {
  code: '133740000',
  display: 'Nakali cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NepaleseHillZebuCattleBreedOrganism: Coding = {
  code: '133741001',
  display: 'Nepalese Hill Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NQuoteGaoundereCattleBreedOrganism: Coding = {
  code: '133742008',
  display: "N'Gaoundere cattle breed (organism)",
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NkediCattleBreedOrganism: Coding = {
  code: '133743003',
  display: 'Nkedi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorthBangladeshGrayCattleBreedOrganism: Coding = {
  code: '133744009',
  display: 'North Bangladesh Gray cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorthSomaliZebuCattleBreedOrganism: Coding = {
  code: '133745005',
  display: 'North Somali Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolledGuzeratCattleBreedOrganism: Coding = {
  code: '133746006',
  display: 'Polled Guzerat cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MandalongCattleBreedOrganism: Coding = {
  code: '133747002',
  display: 'Mandalong cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustralianMilkingZebuCattleBreedOrganism: Coding = {
  code: '133748007',
  display: 'Australian Milking Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedBrangusCattleBreedOrganism: Coding = {
  code: '133749004',
  display: 'Red Brangus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SantaCruzCattleBreedOrganism: Coding = {
  code: '133750004',
  display: 'Santa Cruz cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SiboneyCattleBreedOrganism: Coding = {
  code: '133751000',
  display: 'Siboney cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BambaraCattleBreedOrganism: Coding = {
  code: '133752007',
  display: 'Bambara cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolledNeloreCattleBreedOrganism: Coding = {
  code: '133753002',
  display: 'Polled Nelore cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PrewakwaCattleBreedOrganism: Coding = {
  code: '133754008',
  display: 'Prewakwa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PulMQuoteBorCattleBreedOrganism: Coding = {
  code: '133755009',
  display: "Pul-M'bor cattle breed (organism)",
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PunganurCattleBreedOrganism: Coding = {
  code: '133756005',
  display: 'Punganur cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RamgarhiCattleBreedOrganism: Coding = {
  code: '133757001',
  display: 'Ramgarhi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedBororoCattleBreedOrganism: Coding = {
  code: '133758006',
  display: 'Red Bororo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedDesertCattleBreedOrganism: Coding = {
  code: '133759003',
  display: 'Red Desert cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedKandhariCattleBreedOrganism: Coding = {
  code: '133760008',
  display: 'Red Kandhari cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShakhansurriCattleBreedOrganism: Coding = {
  code: '133761007',
  display: 'Shakhansurri cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShekoCattleBreedOrganism: Coding = {
  code: '133762000',
  display: 'Sheko cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BambeyCattleBreedOrganism: Coding = {
  code: '133763005',
  display: 'Bambey cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BatanesBlackCattleBreedOrganism: Coding = {
  code: '133764004',
  display: 'Batanes Black cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BorgouCattleBreedOrganism: Coding = {
  code: '133765003',
  display: 'Borgou cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrahornCattleBreedOrganism: Coding = {
  code: '133766002',
  display: 'Brahorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BralersCattleBreedOrganism: Coding = {
  code: '133767006',
  display: 'Bralers cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BraMaineCattleBreedOrganism: Coding = {
  code: '133768001',
  display: 'Bra-Maine cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShendiCattleBreedOrganism: Coding = {
  code: '133769009',
  display: 'Shendi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShuwaCattleBreedOrganism: Coding = {
  code: '133770005',
  display: 'Shuwa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SinhalaCattleBreedOrganism: Coding = {
  code: '133771009',
  display: 'Sinhala cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SistaniCattleBreedOrganism: Coding = {
  code: '133772002',
  display: 'Sistani cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SmallEastAfricanZebuCattleBreedOrganism: Coding = {
  code: '133773007',
  display: 'Small East African Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SokotoGudaliCattleBreedOrganism: Coding = {
  code: '133774001',
  display: 'Sokoto Gudali cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SomaliCattleBreedOrganism: Coding = {
  code: '133775000',
  display: 'Somali cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SonkheriCattleBreedOrganism: Coding = {
  code: '133776004',
  display: 'Sonkheri cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SonValleyCattleBreedOrganism: Coding = {
  code: '133777008',
  display: 'Son Valley cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthChinaZebuCattleBreedOrganism: Coding = {
  code: '133778003',
  display: 'South China Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BraSwissCattleBreedOrganism: Coding = {
  code: '133779006',
  display: 'Bra-Swiss cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BravonCattleBreedOrganism: Coding = {
  code: '133780009',
  display: 'Bravon cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrazilianDairyHybridCattleOrganism: Coding = {
  code: '133781008',
  display: 'Brazilian Dairy hybrid cattle (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BurmeseCattleBreedOrganism: Coding = {
  code: '133782001',
  display: 'Burmese cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BushuevCattleBreedOrganism: Coding = {
  code: '133783006',
  display: 'Bushuev cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CaiuaCattleBreedOrganism: Coding = {
  code: '133784000',
  display: 'Caiua cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthMalawiZebuCattleBreedOrganism: Coding = {
  code: '133785004',
  display: 'South Malawi Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SudaneseFulaniCattleBreedOrganism: Coding = {
  code: '133786003',
  display: 'Sudanese Fulani cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TabapuaCattleBreedOrganism: Coding = {
  code: '133787007',
  display: 'Tabapua cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TamankaduwaCattleBreedOrganism: Coding = {
  code: '133788002',
  display: 'Tamankaduwa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TanzanianZebuCattleBreedOrganism: Coding = {
  code: '133789005',
  display: 'Tanzanian Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TaraiCattleBreedOrganism: Coding = {
  code: '133790001',
  display: 'Tarai cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThillariCattleBreedOrganism: Coding = {
  code: '133791002',
  display: 'Thillari cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ToposaCattleBreedOrganism: Coding = {
  code: '133792009',
  display: 'Toposa cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ToronkeCattleBreedOrganism: Coding = {
  code: '133793004',
  display: 'Toronke cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ToupouriCattleBreedOrganism: Coding = {
  code: '133794005',
  display: 'Toupouri cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CarazebuCattleBreedOrganism: Coding = {
  code: '133795006',
  display: 'Carazebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CentralAsianZebuCattleBreedOrganism: Coding = {
  code: '133796007',
  display: 'Central Asian Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CharfordCattleBreedOrganism: Coding = {
  code: '133797003',
  display: 'Charford cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CubanCriolloCattleBreedOrganism: Coding = {
  code: '133798008',
  display: 'Cuban Criollo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CubanZebuCattleBreedOrganism: Coding = {
  code: '133799000',
  display: 'Cuban Zebu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DishtyCattleBreedOrganism: Coding = {
  code: '133800001',
  display: 'Dishty cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DjakoreCattleBreedOrganism: Coding = {
  code: '133801002',
  display: 'Djakore cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GambianNQuoteDamaCattleBreedOrganism: Coding = {
  code: '133802009',
  display: "Gambian N'Dama cattle breed (organism)",
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GhanaSangaCattleBreedOrganism: Coding = {
  code: '133803004',
  display: 'Ghana Sanga cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GirolandoCattleBreedOrganism: Coding = {
  code: '133804005',
  display: 'Girolando cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuzerandoCattleBreedOrganism: Coding = {
  code: '133805006',
  display: 'Guzerando cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HattonCattleBreedOrganism: Coding = {
  code: '133806007',
  display: 'Hatton cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IbageCattleBreedOrganism: Coding = {
  code: '133807003',
  display: 'Ibage cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IraqiCattleBreedOrganism: Coding = {
  code: '133808008',
  display: 'Iraqi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JerdiCattleBreedOrganism: Coding = {
  code: '133809000',
  display: 'Jerdi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JersindCattleBreedOrganism: Coding = {
  code: '133810005',
  display: 'Jersind cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JotkoCattleBreedOrganism: Coding = {
  code: '133811009',
  display: 'Jotko cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KanemCattleBreedOrganism: Coding = {
  code: '133812002',
  display: 'Kanem cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KetekuCattleBreedOrganism: Coding = {
  code: '133813007',
  display: 'Keteku cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LaviniaCattleBreedOrganism: Coding = {
  code: '133814001',
  display: 'Lavinia cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LocalIndianDairyCattleBreedOrganism: Coding = {
  code: '133815000',
  display: 'Local Indian Dairy cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MantiqueiraCattleBreedOrganism: Coding = {
  code: '133816004',
  display: 'Mantiqueira cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NdaguCattleBreedOrganism: Coding = {
  code: '133817008',
  display: 'Ndagu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NormanzuCattleBreedOrganism: Coding = {
  code: '133818003',
  display: 'Normanzu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NubaMountainCattleBreedOrganism: Coding = {
  code: '133819006',
  display: 'Nuba Mountain cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PabnaCattleBreedOrganism: Coding = {
  code: '133820000',
  display: 'Pabna cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MixedPerijaneroCattleBreedOrganism: Coding = {
  code: '133821001',
  display: 'Mixed Perijanero cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PitangueirasCattleBreedOrganism: Coding = {
  code: '133822008',
  display: 'Pitangueiras cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_QuasahCattleBreedOrganism: Coding = {
  code: '133823003',
  display: 'Quasah cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RanaCattleOrganism: Coding = {
  code: '133824009',
  display: 'Rana cattle (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RangerCattleOrganism: Coding = {
  code: '133825005',
  display: 'Ranger cattle (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ReniteloCattleBreedOrganism: Coding = {
  code: '133826006',
  display: 'Renitelo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RiopardenzeCattleBreedOrganism: Coding = {
  code: '133827002',
  display: 'Riopardenze cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RustaqiCattleBreedOrganism: Coding = {
  code: '133828007',
  display: 'Rustaqi cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SabreCattleBreedOrganism: Coding = {
  code: '133829004',
  display: 'Sabre cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SahfordCattleBreedOrganism: Coding = {
  code: '133830009',
  display: 'Sahford cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SchwyzZeboidCattleBreedOrganism: Coding = {
  code: '133831008',
  display: 'Schwyz-Zeboid cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SuiaCattleBreedOrganism: Coding = {
  code: '133832001',
  display: 'Suia cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SuisbuCattleBreedOrganism: Coding = {
  code: '133833006',
  display: 'Suisbu cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SunandiniCattleBreedOrganism: Coding = {
  code: '133834000',
  display: 'Sunandini cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TainoCattleBreedOrganism: Coding = {
  code: '133835004',
  display: 'Taino cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThibarCattleBreedOrganism: Coding = {
  code: '133836003',
  display: 'Thibar cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ToubouCattleBreedOrganism: Coding = {
  code: '133837007',
  display: 'Toubou cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TropicalCattleBreedOrganism: Coding = {
  code: '133838002',
  display: 'Tropical cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TSSH1CattleBreedOrganism: Coding = {
  code: '133839005',
  display: 'TSSH-1 cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_VictoriaCattleBreedOrganism: Coding = {
  code: '133840007',
  display: 'Victoria cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WokalupCattleBreedOrganism: Coding = {
  code: '133841006',
  display: 'Wokalup cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MaduraCattleBreedOrganism: Coding = {
  code: '133842004',
  display: 'Madura cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShireHorseOrganism: Coding = {
  code: '13487004',
  display: 'Shire horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BeltedGallowayCattleBreedOrganism: Coding = {
  code: '13544004',
  display: 'Belted Galloway cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PanamaSheepOrganism: Coding = {
  code: '13934009',
  display: 'Panama sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FHCElitePig9Organism: Coding = {
  code: '14063001',
  display: 'FHC elite pig 9 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YucaPigOrganism: Coding = {
  code: '15443006',
  display: 'Yuca pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BritishSaddlebackPigOrganism: Coding = {
  code: '15961007',
  display: 'British saddleback pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AngloNubianGoatOrganism: Coding = {
  code: '16015002',
  display: 'Anglo nubian goat (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BeefmasterCattleBreedOrganism: Coding = {
  code: '16042001',
  display: 'Beefmaster cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PicCambourghPigOrganism: Coding = {
  code: '17717005',
  display: 'Pic Cambourgh pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TrakehnerHorseOrganism: Coding = {
  code: '1789009',
  display: 'Trakehner horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig72Organism: Coding = {
  code: '18212001',
  display: 'Boar power pig 72 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OrpingtonChickenOrganism: Coding = {
  code: '18299006',
  display: 'Orpington chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HunterHorseOrganism: Coding = {
  code: '19356005',
  display: 'Hunter horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HerefordPigOrganism: Coding = {
  code: '19770007',
  display: 'Hereford pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PietrainPigOrganism: Coding = {
  code: '20044005',
  display: 'Pietrain pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HampshirePigOrganism: Coding = {
  code: '20280002',
  display: 'Hampshire pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WhiteTurkeyOrganism: Coding = {
  code: '2034000',
  display: 'White turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MeleagrisGallopavoOrganism: Coding = {
  code: '20657003',
  display: 'Meleagris gallopavo (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LargeBlackPigOrganism: Coding = {
  code: '21021000',
  display: 'Large black pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SaanenGoatOrganism: Coding = {
  code: '21208000',
  display: 'Saanen goat (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RambouilletSheepOrganism: Coding = {
  code: '2124007',
  display: 'Rambouillet sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MorganHorseOrganism: Coding = {
  code: '21295007',
  display: 'Morgan horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LuingCattleBreedOrganism: Coding = {
  code: '21553004',
  display: 'Luing cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanadianCattleBreedOrganism: Coding = {
  code: '21921002',
  display: 'Canadian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CommercialWhiteEggLayerTypeChickenOrganism: Coding = {
  code: '22330003',
  display: 'Commercial white egg layer type chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BritishLopPigOrganism: Coding = {
  code: '22506004',
  display: 'British lop pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MaransChickenOrganism: Coding = {
  code: '2253004',
  display: 'Marans chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelgianHorseOrganism: Coding = {
  code: '22720009',
  display: 'Belgian horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JapaneseChickenOrganism: Coding = {
  code: '2333000',
  display: 'Japanese chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DomesticGooseOrganism: Coding = {
  code: '23469003',
  display: 'Domestic goose (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HaysConverterCattleBreedOrganism: Coding = {
  code: '23629009',
  display: 'Hays converter cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackWingedBronzeTurkeyOrganism: Coding = {
  code: '23992007',
  display: 'Black-winged bronze turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FHCElitePig3Organism: Coding = {
  code: '24111007',
  display: 'FHC elite pig 3 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig414Organism: Coding = {
  code: '24319000',
  display: 'Boar power pig 414 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PekinDuckOrganism: Coding = {
  code: '24438005',
  display: 'Pekin duck (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CPFPigOrganism: Coding = {
  code: '24840008',
  display: 'CPF pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PennaPalmTurkeyOrganism: Coding = {
  code: '24886005',
  display: 'Penna palm turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DorsetSheepSuperbreedOrganism: Coding = {
  code: '25327001',
  display: 'Dorset sheep superbreed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WestphalianHorseOrganism: Coding = {
  code: '25369002',
  display: 'Westphalian horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BarbadosSheepOrganism: Coding = {
  code: '25660007',
  display: 'Barbados sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HolsteinerHorseOrganism: Coding = {
  code: '25813002',
  display: 'Holsteiner horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SmallWhiteTurkeyOrganism: Coding = {
  code: '25854005',
  display: 'Small white turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig929Organism: Coding = {
  code: '25856007',
  display: 'Boar power pig 929 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HolsteinFriesianCattleBreedOrganism: Coding = {
  code: '26105007',
  display: 'Holstein-Friesian cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WhiteParkCattleBreedOrganism: Coding = {
  code: '26525003',
  display: 'White Park cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NewForestPonyOrganism: Coding = {
  code: '26699009',
  display: 'New Forest pony (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanCreamHorseOrganism: Coding = {
  code: '26837006',
  display: 'American cream horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanTrotterHorseOrganism: Coding = {
  code: '26973000',
  display: 'American trotter horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SlateTurkeyOrganism: Coding = {
  code: '28138009',
  display: 'Slate turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrangusCattleBreedOrganism: Coding = {
  code: '28336001',
  display: 'Brangus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SwissAlpineGoatOrganism: Coding = {
  code: '28360002',
  display: 'Swiss alpine goat (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SimmentalCattleBreedOrganism: Coding = {
  code: '28483003',
  display: 'Simmental cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackWelshCattleBreedOrganism: Coding = {
  code: '28744004',
  display: 'Black Welsh cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig565Organism: Coding = {
  code: '29223008',
  display: 'Boar power pig 565 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PicLinePig26Organism: Coding = {
  code: '29235007',
  display: 'Pic line pig 26 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FaverolleChickenOrganism: Coding = {
  code: '29546006',
  display: 'Faverolle chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LaFlecheChickenOrganism: Coding = {
  code: '29615001',
  display: 'La Fleche chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ConnorPrairiePigOrganism: Coding = {
  code: '29881002',
  display: 'Connor prairie pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ToggenburgGoatOrganism: Coding = {
  code: '30089001',
  display: 'Toggenburg goat (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BelgianBeardedChickenOrganism: Coding = {
  code: '30221007',
  display: 'Belgian bearded chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrahmaCattleBreedOrganism: Coding = {
  code: '30384003',
  display: 'Brahma cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BroadBreastedBronzeTurkeyOrganism: Coding = {
  code: '30432005',
  display: 'Broad breasted bronze turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LacombePigOrganism: Coding = {
  code: '30448006',
  display: 'Lacombe pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TamworthPigOrganism: Coding = {
  code: '30634003',
  display: 'Tamworth pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig84Organism: Coding = {
  code: '30720007',
  display: 'Boar power pig 84 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomneyMarshSheepOrganism: Coding = {
  code: '3099004',
  display: 'Romney marsh sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YorkshireCoachHorseOrganism: Coding = {
  code: '31633003',
  display: 'Yorkshire coach horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomanovSheepOrganism: Coding = {
  code: '32145006',
  display: 'Romanov sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LincolnRedCattleBreedOrganism: Coding = {
  code: '3216001',
  display: 'Lincoln red cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig48Organism: Coding = {
  code: '32297006',
  display: 'Boar power pig 48 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackTurkeyOrganism: Coding = {
  code: '32509001',
  display: 'Black turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CommercialMeatTypeChickenOrganism: Coding = {
  code: '32540008',
  display: 'Commercial meat type chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DurocPigOrganism: Coding = {
  code: '3260001',
  display: 'Duroc pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FHCElitePig7Organism: Coding = {
  code: '32683006',
  display: 'FHC elite pig 7 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GirCattleBreedOrganism: Coding = {
  code: '32938007',
  display: 'Gir cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig616Organism: Coding = {
  code: '33212007',
  display: 'Boar power pig 616 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PhoenixChickenOrganism: Coding = {
  code: '33224000',
  display: 'Phoenix chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KentuckyRedBerkshirePigOrganism: Coding = {
  code: '33551003',
  display: 'Kentucky red berkshire pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ButtercupChickenOrganism: Coding = {
  code: '33607002',
  display: 'Buttercup chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_StandardbredHorseOrganism: Coding = {
  code: '34200004',
  display: 'Standardbred horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig828Organism: Coding = {
  code: '34595003',
  display: 'Boar power pig 828 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BosTaurusOrganism: Coding = {
  code: '34618005',
  display: 'Bos taurus (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChianinaCattleBreedOrganism: Coding = {
  code: '35229007',
  display: 'Chianina cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EquusCaballusOrganism: Coding = {
  code: '35354009',
  display: 'Equus caballus (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SouthdownSheepOrganism: Coding = {
  code: '3566006',
  display: 'Southdown sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LeghornChickenOrganism: Coding = {
  code: '35839008',
  display: 'Leghorn chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DomesticFowlOrganism: Coding = {
  code: '359839008',
  display: 'Domestic fowl (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KleenLeenBlackPigOrganism: Coding = {
  code: '36111002',
  display: 'Kleen leen black pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SpottedPigOrganism: Coding = {
  code: '36187006',
  display: 'Spotted pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DekalbHybridPigLine31Organism: Coding = {
  code: '36570001',
  display: 'Dekalb hybrid pig line 31 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BroadBreastedWhiteTurkeyOrganism: Coding = {
  code: '37812004',
  display: 'Broad breasted white turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BeltsvilleWhiteTurkeyOrganism: Coding = {
  code: '3872000',
  display: 'Beltsville white turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NormanCoachHorseOrganism: Coding = {
  code: '39532001',
  display: 'Norman coach horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BuckeyeChickenOrganism: Coding = {
  code: '39571003',
  display: 'Buckeye chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArigeoisPonyBreedOrganism: Coding = {
  code: '396488006',
  display: 'Ariégeois pony breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WiltshireHornSheepOrganism: Coding = {
  code: '39855006',
  display: 'Wiltshire horn sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PonyOrganism: Coding = {
  code: '3997000',
  display: 'Pony (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PinzgauerCattleBreedOrganism: Coding = {
  code: '400003',
  display: 'Pinzgauer cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GalwaySheepBreedOrganism: Coding = {
  code: '406660008',
  display: 'Galway sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_UkrainianSteppeWhitePigOrganism: Coding = {
  code: '406663005',
  display: 'Ukrainian steppe white pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrabantHorseOrganism: Coding = {
  code: '406711007',
  display: 'Brabant horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GypsyVannerHorseOrganism: Coding = {
  code: '406714004',
  display: 'Gypsy Vanner horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MurgeseHorseOrganism: Coding = {
  code: '406715003',
  display: 'Murgese horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MixedBreedHorseOrganism: Coding = {
  code: '406721004',
  display: 'Mixed breed horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MixedBreedSheepOrganism: Coding = {
  code: '406722006',
  display: 'Mixed breed sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MixedBreedChickenOrganism: Coding = {
  code: '406723001',
  display: 'Mixed breed chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WarmbloodHorseBreedOrganism: Coding = {
  code: '407402001',
  display: 'Warmblood horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackAngusCattleBreedOrganism: Coding = {
  code: '409905004',
  display: 'Black Angus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MixedBreedCattleOrganism: Coding = {
  code: '409906003',
  display: 'Mixed breed cattle (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MasaiCattleBreedOrganism: Coding = {
  code: '409908002',
  display: 'Masai cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AntwerpBeardedBantamOrganism: Coding = {
  code: '409909005',
  display: 'Antwerp bearded bantam (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BantamOrganism: Coding = {
  code: '409910000',
  display: 'Bantam (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MoulardDuckBreedOrganism: Coding = {
  code: '409911001',
  display: 'Moulard duck breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ClevelandBayHorseOrganism: Coding = {
  code: '41092008',
  display: 'Cleveland bay horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PlymouthRockChickenOrganism: Coding = {
  code: '41290008',
  display: 'Plymouth Rock chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JungleFowlChickenOrganism: Coding = {
  code: '41437006',
  display: 'Jungle fowl chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DekalbHybridPigLine30Organism: Coding = {
  code: '41561001',
  display: 'Dekalb hybrid pig line 30 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MixedBreedPigOrganism: Coding = {
  code: '417012009',
  display: 'Mixed breed pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PerendaleSheepOrganism: Coding = {
  code: '41706005',
  display: 'Perendale sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PalominoHorseOrganism: Coding = {
  code: '41738000',
  display: 'Palomino horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LipizzanerHorseOrganism: Coding = {
  code: '41754002',
  display: 'Lipizzaner horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BisonBisonXSimmentalHybridCattleBreedOrganism: Coding = {
  code: '424705003',
  display: 'Bison bison X Simmental hybrid cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BisonBisonXBosTaurusHybridOrganism: Coding = {
  code: '425181009',
  display: 'Bison bison X Bos taurus hybrid (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MinorcaChickenOrganism: Coding = {
  code: '42602001',
  display: 'Minorca chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SaddlebredHorseSuperbreedOrganism: Coding = {
  code: '427136006',
  display: 'Saddlebred horse superbreed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanSaddlebredHorseOrganism: Coding = {
  code: '42724005',
  display: 'American saddlebred horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JerseyGiantChickenOrganism: Coding = {
  code: '42840001',
  display: 'Jersey giant chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThoroughbredHorseOrganism: Coding = {
  code: '4288003',
  display: 'Thoroughbred horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorwegianLandracePigOrganism: Coding = {
  code: '42948007',
  display: 'Norwegian landrace pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YokohamaChickenOrganism: Coding = {
  code: '43223009',
  display: 'Yokohama chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig454Organism: Coding = {
  code: '43500007',
  display: 'Boar power pig 454 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RouenDuckBreedOrganism: Coding = {
  code: '43869002',
  display: 'Rouen duck breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolledDorsetSheepBreedOrganism: Coding = {
  code: '440034003',
  display: 'Polled dorset sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RhinelanderHorseBreedOrganism: Coding = {
  code: '441714000',
  display: 'Rhinelander horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrownSwissCattleBreedOrganism: Coding = {
  code: '44230005',
  display: 'Brown Swiss cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TarpanHorseBreedOrganism: Coding = {
  code: '445729005',
  display: 'Tarpan horse breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DebouilletSheepOrganism: Coding = {
  code: '44835005',
  display: 'Debouillet sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MaineAnjouCattleBreedOrganism: Coding = {
  code: '45284002',
  display: 'Maine Anjou cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FHCElitePig1Organism: Coding = {
  code: '45635003',
  display: 'FHC elite pig 1 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MouflonSheepOrganism: Coding = {
  code: '45690005',
  display: 'Mouflon sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShropshireSheepOrganism: Coding = {
  code: '4574003',
  display: 'Shropshire sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanAlbinoHorseOrganism: Coding = {
  code: '45790002',
  display: 'American Albino horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DelaineMerinoSheepOrganism: Coding = {
  code: '46392004',
  display: 'Delaine merino sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanPonyOrganism: Coding = {
  code: '46408008',
  display: 'American pony (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MidgetWhiteTurkeyOrganism: Coding = {
  code: '46830002',
  display: 'Midget white turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GallusGallusOrganism: Coding = {
  code: '47290002',
  display: 'Gallus gallus (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DarkBrownTurkeyOrganism: Coding = {
  code: '47314009',
  display: 'Dark brown turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JavaChickenOrganism: Coding = {
  code: '47745005',
  display: 'Java chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FHCElitePig4Organism: Coding = {
  code: '47795006',
  display: 'FHC elite pig 4 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CanadianHorseOrganism: Coding = {
  code: '47842004',
  display: 'Canadian horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SilkieChickenOrganism: Coding = {
  code: '48255007',
  display: 'Silkie chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BeltsvillePigOrganism: Coding = {
  code: '48394005',
  display: 'Beltsville pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig656Organism: Coding = {
  code: '48470006',
  display: 'Boar power pig 656 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ClunForestSheepOrganism: Coding = {
  code: '48697009',
  display: 'Clun Forest sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LimousinCattleBreedOrganism: Coding = {
  code: '48702000',
  display: 'Limousin cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PiedmonteseCattleBreedOrganism: Coding = {
  code: '4880003',
  display: 'Piedmontese cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PalousePigOrganism: Coding = {
  code: '49240006',
  display: 'Palouse pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LankenvelderChickenOrganism: Coding = {
  code: '49407004',
  display: 'Lankenvelder chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FHCElitePig6Organism: Coding = {
  code: '49462008',
  display: 'FHC elite pig 6 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PeruvianPasoHorseOrganism: Coding = {
  code: '4960000',
  display: 'Peruvian Paso horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlueSlateTurkeyOrganism: Coding = {
  code: '50710008',
  display: 'Blue slate turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CheviotSheepOrganism: Coding = {
  code: '50717006',
  display: 'Cheviot sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TarentaiseCattleBreedOrganism: Coding = {
  code: '50959000',
  display: 'Tarentaise cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TennesseeWalkingHorseOrganism: Coding = {
  code: '51023000',
  display: 'Tennessee walking horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FayoumiChickenOrganism: Coding = {
  code: '5114005',
  display: 'Fayoumi chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MontdaleSheepOrganism: Coding = {
  code: '5164003',
  display: 'Montdale sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JerseyCattleBreedOrganism: Coding = {
  code: '51937006',
  display: 'Jersey cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OxfordSandyBlockPigOrganism: Coding = {
  code: '5227002',
  display: 'Oxford sandy block pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DominiqueChickenOrganism: Coding = {
  code: '52882006',
  display: 'Dominique chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DexterCattleBreedOrganism: Coding = {
  code: '53031002',
  display: 'Dexter cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OxfordDownSheepOrganism: Coding = {
  code: '53360003',
  display: 'Oxford Down sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig59Organism: Coding = {
  code: '53431006',
  display: 'Boar power pig 59 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SantaGertrudisCattleBreedOrganism: Coding = {
  code: '5345000',
  display: 'Santa Gertrudis cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RhodeIslandRedChickenOrganism: Coding = {
  code: '5350006',
  display: 'Rhode Island red chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SuffolkHorseOrganism: Coding = {
  code: '53567001',
  display: 'Suffolk horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ArabianHorseOrganism: Coding = {
  code: '54098002',
  display: 'Arabian horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NewHampshireChickenOrganism: Coding = {
  code: '54218009',
  display: 'New Hampshire chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WelshPigOrganism: Coding = {
  code: '54232006',
  display: 'Welsh pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AustralorpChickenOrganism: Coding = {
  code: '54251000',
  display: 'Australorp chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FrenchAlpineGoatOrganism: Coding = {
  code: '5438004',
  display: 'French alpine goat (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HaflingerHorseOrganism: Coding = {
  code: '54447000',
  display: 'Haflinger horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GrayTurkeyOrganism: Coding = {
  code: '54604007',
  display: 'Gray turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanMiniatureHorseOrganism: Coding = {
  code: '54699009',
  display: 'American miniature horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_JerseyBuffTurkeyOrganism: Coding = {
  code: '55074002',
  display: 'Jersey buff turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AppaloosaHorseOrganism: Coding = {
  code: '55167009',
  display: 'Appaloosa horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LaManchaGoatOrganism: Coding = {
  code: '55530007',
  display: 'La Mancha goat (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig292Organism: Coding = {
  code: '56084008',
  display: 'Boar power pig 292 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PasoFinoHorseOrganism: Coding = {
  code: '56086005',
  display: 'Paso Fino horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HollandChickenOrganism: Coding = {
  code: '56764001',
  display: 'Holland chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrahmaChickenOrganism: Coding = {
  code: '57364006',
  display: 'Brahma chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KleenLeenWhitePigOrganism: Coding = {
  code: '57613003',
  display: 'Kleen leen white pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SpitzhaubenChickenOrganism: Coding = {
  code: '57950004',
  display: 'Spitzhauben chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PintoOrganism: Coding = {
  code: '58264006',
  display: 'Pinto (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DutchLandracePigOrganism: Coding = {
  code: '58311005',
  display: 'Dutch landrace pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NavajoSheepOrganism: Coding = {
  code: '59210004',
  display: 'Navajo sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FHCElitePig2Organism: Coding = {
  code: '59667000',
  display: 'FHC elite pig 2 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DekalbHybridPigLine33Organism: Coding = {
  code: '6053007',
  display: 'Dekalb hybrid pig line 33 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LilacBuffTurkeyOrganism: Coding = {
  code: '60786005',
  display: 'Lilac buff turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MarylandPigOrganism: Coding = {
  code: '60958006',
  display: 'Maryland pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig545Organism: Coding = {
  code: '61036003',
  display: 'Boar power pig 545 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MinnesotaPigOrganism: Coding = {
  code: '61083001',
  display: 'Minnesota pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MeusseRhineIjsselCattleBreedOrganism: Coding = {
  code: '6112002',
  display: 'Meusse-Rhine-Ijssel cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DorkingChickenOrganism: Coding = {
  code: '61204004',
  display: 'Dorking chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CornishChickenOrganism: Coding = {
  code: '61839006',
  display: 'Cornish chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolishChickenOrganism: Coding = {
  code: '61843005',
  display: 'Polish chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DekalbHybridPigLine61Organism: Coding = {
  code: '61973002',
  display: 'Dekalb hybrid pig line 61 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RoyalPalmTurkeyOrganism: Coding = {
  code: '62124009',
  display: 'Royal palm turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlondeDQuoteAquitaineOrganism: Coding = {
  code: '62153005',
  display: "Blonde d'Aquitaine (organism)",
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GalicenoHorseOrganism: Coding = {
  code: '6220006',
  display: 'Galiceno horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChanteclerChickenOrganism: Coding = {
  code: '63257005',
  display: 'Chantecler chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AngoraGoatOrganism: Coding = {
  code: '64158000',
  display: 'Angora goat (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LeicesterSheepOrganism: Coding = {
  code: '6431001',
  display: 'Leicester sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KarakulSheepOrganism: Coding = {
  code: '64591001',
  display: 'Karakul sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LightBrownTurkeyOrganism: Coding = {
  code: '65136005',
  display: 'Light brown turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackFacedHighlandSheepOrganism: Coding = {
  code: '65187008',
  display: 'Black faced Highland sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MarchigianaCattleBreedOrganism: Coding = {
  code: '65344003',
  display: 'Marchigiana cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LincolnSheepOrganism: Coding = {
  code: '65492002',
  display: 'Lincoln sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BarnevelderChickenOrganism: Coding = {
  code: '65873005',
  display: 'Barnevelder chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HanoverianHorseOrganism: Coding = {
  code: '66168008',
  display: 'Hanoverian horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LonghornCattleBreedOrganism: Coding = {
  code: '66314009',
  display: 'Longhorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GallowayCattleBreedOrganism: Coding = {
  code: '66911005',
  display: 'Galloway cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SebrightChickenOrganism: Coding = {
  code: '67021006',
  display: 'Sebright chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TurkenChickenOrganism: Coding = {
  code: '67209003',
  display: 'Turken chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CotswoldSheepOrganism: Coding = {
  code: '67414001',
  display: 'Cotswold sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanFleckViehCattleBreedOrganism: Coding = {
  code: '67448000',
  display: 'German Fleck-Vieh cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CorriedaleSheepOrganism: Coding = {
  code: '67515002',
  display: 'Corriedale sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FHCElitePig5Organism: Coding = {
  code: '67720004',
  display: 'FHC elite pig 5 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PygmyGoatOrganism: Coding = {
  code: '684003',
  display: 'Pygmy goat (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SultanChickenOrganism: Coding = {
  code: '68412000',
  display: 'Sultan chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig141Organism: Coding = {
  code: '68512002',
  display: 'Boar power pig 141 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShetlandPonyOrganism: Coding = {
  code: '69067004',
  display: 'Shetland pony (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChesterWhitePigOrganism: Coding = {
  code: '69461005',
  display: 'Chester white pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AnconaChickenOrganism: Coding = {
  code: '69575001',
  display: 'Ancona chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KleenLeenPigOrganism: Coding = {
  code: '69602006',
  display: 'Kleen leen pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OrloffChickenOrganism: Coding = {
  code: '70210006',
  display: 'Orloff chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LangshanChickenOrganism: Coding = {
  code: '70308007',
  display: 'Langshan chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CubalayaChickenOrganism: Coding = {
  code: '70326005',
  display: 'Cubalaya chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GameChickenOrganism: Coding = {
  code: '70429002',
  display: 'Game chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AlpineGoatOrganism: Coding = {
  code: '70431006',
  display: 'Alpine goat (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_IcelandicHorseOrganism: Coding = {
  code: '70457009',
  display: 'Icelandic horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AnserAnserAnserOrganism: Coding = {
  code: '70881005',
  display: 'Anser anser anser (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CommercialBrownEggLayerTypeChickenOrganism: Coding = {
  code: '71238008',
  display: 'Commercial brown egg layer type chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ItalianLandracePigOrganism: Coding = {
  code: '71923001',
  display: 'Italian landrace pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FinnishLandraceSheepOrganism: Coding = {
  code: '72329005',
  display: 'Finnish landrace sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanTunisHorseOrganism: Coding = {
  code: '72394007',
  display: 'American tunis horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SuffolkSheepOrganism: Coding = {
  code: '72648002',
  display: 'Suffolk sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FHCElitePig8Organism: Coding = {
  code: '73005003',
  display: 'FHC elite pig 8 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PennaWhiteTurkeyOrganism: Coding = {
  code: '73012007',
  display: 'Penna white turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanMerinoSheepOrganism: Coding = {
  code: '73191001',
  display: 'American merino sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WessexSaddlebackPigOrganism: Coding = {
  code: '73648005',
  display: 'Wessex saddleback pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MinnesotaPigNumber1Organism: Coding = {
  code: '73944009',
  display: 'Minnesota pig #1 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MontanaPigOrganism: Coding = {
  code: '74517004',
  display: 'Montana pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedWattlePigOrganism: Coding = {
  code: '74568001',
  display: 'Red wattle pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RockAlpineGoatOrganism: Coding = {
  code: '74745008',
  display: 'Rock alpine goat (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPigOrganism: Coding = {
  code: '74899005',
  display: 'Boar power pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DekalbHybridPigLineOrganism: Coding = {
  code: '74921000',
  display: 'Dekalb hybrid pig line (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig161Organism: Coding = {
  code: '74970001',
  display: 'Boar power pig 161 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PicPigOrganism: Coding = {
  code: '75709004',
  display: 'Pic pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WyandotteChickenOrganism: Coding = {
  code: '7598004',
  display: 'Wyandotte chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanPaintHorseOrganism: Coding = {
  code: '7623008',
  display: 'American paint horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CampineChickenOrganism: Coding = {
  code: '76268003',
  display: 'Campine chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SpanishMustangHorseOrganism: Coding = {
  code: '76302002',
  display: 'Spanish mustang horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig27Organism: Coding = {
  code: '76364003',
  display: 'Boar power pig 27 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_QuarterHorseOrganism: Coding = {
  code: '76467006',
  display: 'Quarter horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GelbveihCattleBreedOrganism: Coding = {
  code: '76497003',
  display: 'Gelbveih cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NelloreCattleBreedOrganism: Coding = {
  code: '76604009',
  display: 'Nellore cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NebraskanSpottedTurkeyOrganism: Coding = {
  code: '7700003',
  display: 'Nebraskan spotted turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LargeWhitePigOrganism: Coding = {
  code: '77236002',
  display: 'Large white pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LamonaChickenOrganism: Coding = {
  code: '7767003',
  display: 'Lamona chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ThuringerChickenOrganism: Coding = {
  code: '77839002',
  display: 'Thuringer chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MalayChickenOrganism: Coding = {
  code: '78154009',
  display: 'Malay chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AndalusianChickenOrganism: Coding = {
  code: '78375007',
  display: 'Andalusian chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HornedHerefordOrganism: Coding = {
  code: '7843000',
  display: 'Horned Hereford (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MilkingShorthornCattleBreedOrganism: Coding = {
  code: '78541007',
  display: 'Milking Shorthorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SusScrofaOrganism: Coding = {
  code: '78678003',
  display: 'Sus scrofa (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BritishLandracePigOrganism: Coding = {
  code: '78994007',
  display: 'British landrace pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RomedaleSheepOrganism: Coding = {
  code: '79603002',
  display: 'Romedale sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolandChinaPigOrganism: Coding = {
  code: '79814001',
  display: 'Poland China pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LuciePigOrganism: Coding = {
  code: '80084005',
  display: 'Lucie pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_LandracePigOrganism: Coding = {
  code: '80131009',
  display: 'Landrace pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HungarianChickenOrganism: Coding = {
  code: '80204002',
  display: 'Hungarian chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NarragansettTurkeyOrganism: Coding = {
  code: '80610007',
  display: 'Narragansett turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AndalusianHorseOrganism: Coding = {
  code: '80777007',
  display: 'Andalusian horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ShorthornCattleBreedOrganism: Coding = {
  code: '80835003',
  display: 'Shorthorn cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RosecombChickenOrganism: Coding = {
  code: '80886008',
  display: 'Rosecomb chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WelshWalkingHorseOrganism: Coding = {
  code: '8089006',
  display: 'Welsh walking horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SanPierrePigOrganism: Coding = {
  code: '80979001',
  display: 'San Pierre pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SussexChickenOrganism: Coding = {
  code: '80993008',
  display: 'Sussex chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NormandieCattleBreedOrganism: Coding = {
  code: '81267004',
  display: 'Normandie cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CaliforniaChickenOrganism: Coding = {
  code: '82249009',
  display: 'California chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HampshireDownSheepOrganism: Coding = {
  code: '82440005',
  display: 'Hampshire Down sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HoudanChickenOrganism: Coding = {
  code: '82848009',
  display: 'Houdan chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_MiddleWhitePigOrganism: Coding = {
  code: '82909008',
  display: 'Middle white pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ScottishHighlandCattleBreedOrganism: Coding = {
  code: '83173002',
  display: 'Scottish Highland cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HungarianGooseOrganism: Coding = {
  code: '83842004',
  display: 'Hungarian goose (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CriolloCattleBreedOrganism: Coding = {
  code: '83996001',
  display: 'Criollo cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig747Organism: Coding = {
  code: '84081007',
  display: 'Boar power pig 747 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_KleenLeenRedPigOrganism: Coding = {
  code: '84232003',
  display: 'Kleen leen red pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig474Organism: Coding = {
  code: '84315000',
  display: 'Boar power pig 474 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DanishLandracePigOrganism: Coding = {
  code: '84528008',
  display: 'Danish landrace pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GuernseyCattleBreedOrganism: Coding = {
  code: '84839000',
  display: 'Guernsey cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AberdeenAngusCattleBreedOrganism: Coding = {
  code: '84923006',
  display: 'Aberdeen Angus cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_DekalbHybridPigLine51Organism: Coding = {
  code: '8516002',
  display: 'Dekalb hybrid pig line 51 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_YorkshirePigOrganism: Coding = {
  code: '85315007',
  display: 'Yorkshire pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_EquusAsinusOrganism: Coding = {
  code: '85626006',
  display: 'Equus asinus (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PicLinePig24Organism: Coding = {
  code: '86440008',
  display: 'Pic line pig 24 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HormelMiniaturePigOrganism: Coding = {
  code: '86694007',
  display: 'Hormel miniature pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_HornedDorsetSheepBreedOrganism: Coding = {
  code: '86920006',
  display: 'Horned dorset sheep breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BoarPowerPig282Organism: Coding = {
  code: '87061000',
  display: 'Boar power pig 282 (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_WelsummerChickenOrganism: Coding = {
  code: '87530001',
  display: 'Welsummer chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GermanLandracePigOrganism: Coding = {
  code: '8763002',
  display: 'German landrace pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SumatraChickenOrganism: Coding = {
  code: '87833007',
  display: 'Sumatra chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_NorthCountyCheviotSheepOrganism: Coding = {
  code: '87962009',
  display: 'North County cheviot sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_RedPollCattleBreedOrganism: Coding = {
  code: '88807001',
  display: 'Red Poll cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FjordHorseOrganism: Coding = {
  code: '89648005',
  display: 'Fjord horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_TargheeSheepOrganism: Coding = {
  code: '89665001',
  display: 'Targhee sheep (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FrenchLandracePigOrganism: Coding = {
  code: '8970009',
  display: 'French landrace pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChamoiseeGoatOrganism: Coding = {
  code: '89708009',
  display: 'Chamoisee goat (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AyrshireCattleBreedOrganism: Coding = {
  code: '8989009',
  display: 'Ayrshire cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FHCPigOrganism: Coding = {
  code: '89928000',
  display: 'FHC pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_AmericanBuckskinHorseOrganism: Coding = {
  code: '90050009',
  display: 'American Buckskin horse (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_SalersCattleBreedOrganism: Coding = {
  code: '90612002',
  display: 'Salers cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_GloucesterOldSpotPigOrganism: Coding = {
  code: '90885005',
  display: 'Gloucester old spot pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_OICPigOrganism: Coding = {
  code: '9135003',
  display: 'OIC pig (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BlackSpanishTurkeyOrganism: Coding = {
  code: '91401000',
  display: 'Black Spanish turkey (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_FrizzleChickenOrganism: Coding = {
  code: '91427000',
  display: 'Frizzle chicken (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_ChineseGooseOrganism: Coding = {
  code: '91460002',
  display: 'Chinese goose (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_CamarronGoatOrganism: Coding = {
  code: '9230001',
  display: 'Camarron goat (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_PolledHerefordOrganism: Coding = {
  code: '9277006',
  display: 'Polled Hereford (organism)',
  system: 'http://snomed.info/sct',
};
const AnimalBreeds_BrownWelshCattleBreedOrganism: Coding = {
  code: '944009',
  display: 'Brown Welsh cattle breed (organism)',
  system: 'http://snomed.info/sct',
};
/**
 * This example value set defines a set of codes that can be used to indicate breeds of species.
 */
export const AnimalBreeds = {
  PercheronHorseOrganism: AnimalBreeds_PercheronHorseOrganism,
  BelgiumLandracePigOrganism: AnimalBreeds_BelgiumLandracePigOrganism,
  ArucanaChickenOrganism: AnimalBreeds_ArucanaChickenOrganism,
  DekalbHybridPigLine77Organism: AnimalBreeds_DekalbHybridPigLine77Organism,
  VikingHorseOrganism: AnimalBreeds_VikingHorseOrganism,
  GujaratiCattleBreedOrganism: AnimalBreeds_GujaratiCattleBreedOrganism,
  NoTailSheepOrganism: AnimalBreeds_NoTailSheepOrganism,
  RomneletSheepOrganism: AnimalBreeds_RomneletSheepOrganism,
  HackneyHorseOrganism: AnimalBreeds_HackneyHorseOrganism,
  BerkshirePigOrganism: AnimalBreeds_BerkshirePigOrganism,
  DekalbHybridPigLine63Organism: AnimalBreeds_DekalbHybridPigLine63Organism,
  RumplessChickenOrganism: AnimalBreeds_RumplessChickenOrganism,
  PilgrimGooseOrganism: AnimalBreeds_PilgrimGooseOrganism,
  DelawareChickenOrganism: AnimalBreeds_DelawareChickenOrganism,
  KerryHillSheepOrganism: AnimalBreeds_KerryHillSheepOrganism,
  MissouriFoxTrottingHorseOrganism: AnimalBreeds_MissouriFoxTrottingHorseOrganism,
  SwedishLandracePigOrganism: AnimalBreeds_SwedishLandracePigOrganism,
  RedcapChickenOrganism: AnimalBreeds_RedcapChickenOrganism,
  ClydesdaleHorseOrganism: AnimalBreeds_ClydesdaleHorseOrganism,
  HerefordCattleSuperbreedOrganism: AnimalBreeds_HerefordCattleSuperbreedOrganism,
  EquusCaballusGmeliniOrganism: AnimalBreeds_EquusCaballusGmeliniOrganism,
  EquusCaballusGmeliniXEquusCaballusCaballusOrganism: AnimalBreeds_EquusCaballusGmeliniXEquusCaballusCaballusOrganism,
  BosFrontalisOrganism: AnimalBreeds_BosFrontalisOrganism,
  CapraHircusOrganism: AnimalBreeds_CapraHircusOrganism,
  OvisAriesOrganism: AnimalBreeds_OvisAriesOrganism,
  MerinoSheepSuperbreedOrganism: AnimalBreeds_MerinoSheepSuperbreedOrganism,
  AnasPlatyrhynchosOrganism: AnimalBreeds_AnasPlatyrhynchosOrganism,
  AnserAnserOrganism: AnimalBreeds_AnserAnserOrganism,
  CrevecoeurChickenOrganism: AnimalBreeds_CrevecoeurChickenOrganism,
  AfricanderCattleBreedOrganism: AnimalBreeds_AfricanderCattleBreedOrganism,
  AnkoleCattleBreedOrganism: AnimalBreeds_AnkoleCattleBreedOrganism,
  AnkoleWatusiCattleBreedOrganism: AnimalBreeds_AnkoleWatusiCattleBreedOrganism,
  BaladiCattleBreedOrganism: AnimalBreeds_BaladiCattleBreedOrganism,
  BelmontRedCattleBreedOrganism: AnimalBreeds_BelmontRedCattleBreedOrganism,
  BonsmaraCattleBreedOrganism: AnimalBreeds_BonsmaraCattleBreedOrganism,
  DamiettaCattleBreedOrganism: AnimalBreeds_DamiettaCattleBreedOrganism,
  HorroCattleBreedOrganism: AnimalBreeds_HorroCattleBreedOrganism,
  KuriCattleBreedOrganism: AnimalBreeds_KuriCattleBreedOrganism,
  NguniCattleBreedOrganism: AnimalBreeds_NguniCattleBreedOrganism,
  PhilippineNativeCattleBreedOrganism: AnimalBreeds_PhilippineNativeCattleBreedOrganism,
  RomagnolaCattleBreedOrganism: AnimalBreeds_RomagnolaCattleBreedOrganism,
  SanheCattleBreedOrganism: AnimalBreeds_SanheCattleBreedOrganism,
  TswanaCattleBreedOrganism: AnimalBreeds_TswanaCattleBreedOrganism,
  TuliCattleBreedOrganism: AnimalBreeds_TuliCattleBreedOrganism,
  AliabDinkaCattleBreedOrganism: AnimalBreeds_AliabDinkaCattleBreedOrganism,
  AlurCattleBreedOrganism: AnimalBreeds_AlurCattleBreedOrganism,
  AnkinaCattleBreedOrganism: AnimalBreeds_AnkinaCattleBreedOrganism,
  ApulianPodolianCattleBreedOrganism: AnimalBreeds_ApulianPodolianCattleBreedOrganism,
  AradoCattleBreedOrganism: AnimalBreeds_AradoCattleBreedOrganism,
  AweilDinkaCattleBreedOrganism: AnimalBreeds_AweilDinkaCattleBreedOrganism,
  BahimaCattleBreedOrganism: AnimalBreeds_BahimaCattleBreedOrganism,
  BapediCattleBreedOrganism: AnimalBreeds_BapediCattleBreedOrganism,
  BariaVietnamMadagascarCattleBreedOrganism: AnimalBreeds_BariaVietnamMadagascarCattleBreedOrganism,
  BarotseCattleBreedOrganism: AnimalBreeds_BarotseCattleBreedOrganism,
  BarraDoCuanzoCattleBreedOrganism: AnimalBreeds_BarraDoCuanzoCattleBreedOrganism,
  BashiCattleBreedOrganism: AnimalBreeds_BashiCattleBreedOrganism,
  BasutoCattleBreedOrganism: AnimalBreeds_BasutoCattleBreedOrganism,
  BatangasCattleBreedOrganism: AnimalBreeds_BatangasCattleBreedOrganism,
  BavendaCattleBreedOrganism: AnimalBreeds_BavendaCattleBreedOrganism,
  BejaCattleBreedOrganism: AnimalBreeds_BejaCattleBreedOrganism,
  CalabrianCattleBreedOrganism: AnimalBreeds_CalabrianCattleBreedOrganism,
  BlondeDuCapBonCattleBreedOrganism: AnimalBreeds_BlondeDuCapBonCattleBreedOrganism,
  ChanDocCattleBreedOrganism: AnimalBreeds_ChanDocCattleBreedOrganism,
  ChernigovCattleBreedOrganism: AnimalBreeds_ChernigovCattleBreedOrganism,
  ChinoSantandereanoCattleBreedOrganism: AnimalBreeds_ChinoSantandereanoCattleBreedOrganism,
  CinisaraCattleBreedOrganism: AnimalBreeds_CinisaraCattleBreedOrganism,
  CupremHybridCattleBreedOrganism: AnimalBreeds_CupremHybridCattleBreedOrganism,
  DabieshanCattleBreedOrganism: AnimalBreeds_DabieshanCattleBreedOrganism,
  DamaraCattleBreedOrganism: AnimalBreeds_DamaraCattleBreedOrganism,
  DanakilCattleBreedOrganism: AnimalBreeds_DanakilCattleBreedOrganism,
  DnieperCattleBreedOrganism: AnimalBreeds_DnieperCattleBreedOrganism,
  DoayoCattleBreedOrganism: AnimalBreeds_DoayoCattleBreedOrganism,
  EasternNuerCattleBreedOrganism: AnimalBreeds_EasternNuerCattleBreedOrganism,
  EgyptianCattleBreedOrganism: AnimalBreeds_EgyptianCattleBreedOrganism,
  FogeraCattleBreedOrganism: AnimalBreeds_FogeraCattleBreedOrganism,
  GarfagninaCattleBreedOrganism: AnimalBreeds_GarfagninaCattleBreedOrganism,
  GratiCattleBreedOrganism: AnimalBreeds_GratiCattleBreedOrganism,
  GaunlingCattleBreedOrganism: AnimalBreeds_GaunlingCattleBreedOrganism,
  HalhinGolCattleBreedOrganism: AnimalBreeds_HalhinGolCattleBreedOrganism,
  HolmongerCattleBreedOrganism: AnimalBreeds_HolmongerCattleBreedOrganism,
  IlocosCattleBreedOrganism: AnimalBreeds_IlocosCattleBreedOrganism,
  IloiloCattleBreedOrganism: AnimalBreeds_IloiloCattleBreedOrganism,
  InkukuCattleBreedOrganism: AnimalBreeds_InkukuCattleBreedOrganism,
  IskarCattleBreedOrganism: AnimalBreeds_IskarCattleBreedOrganism,
  IstrianCattleBreedOrganism: AnimalBreeds_IstrianCattleBreedOrganism,
  JavaneseOngoleCattleBreedOrganism: AnimalBreeds_JavaneseOngoleCattleBreedOrganism,
  JavaneseZebuCattleBreedOrganism: AnimalBreeds_JavaneseZebuCattleBreedOrganism,
  JinnanCattleBreedOrganism: AnimalBreeds_JinnanCattleBreedOrganism,
  KalmykCattleBreedOrganism: AnimalBreeds_KalmykCattleBreedOrganism,
  KaokoveldCattleBreedOrganism: AnimalBreeds_KaokoveldCattleBreedOrganism,
  KazakhWhiteheadCattleBreedOrganism: AnimalBreeds_KazakhWhiteheadCattleBreedOrganism,
  KedahKelantanCattleBreedOrganism: AnimalBreeds_KedahKelantanCattleBreedOrganism,
  KigeziCattleBreedOrganism: AnimalBreeds_KigeziCattleBreedOrganism,
  KisantuCattleBreedOrganism: AnimalBreeds_KisantuCattleBreedOrganism,
  KolubaraCattleBreedOrganism: AnimalBreeds_KolubaraCattleBreedOrganism,
  KurganCattleBreedOrganism: AnimalBreeds_KurganCattleBreedOrganism,
  KyogaCattleBreedOrganism: AnimalBreeds_KyogaCattleBreedOrganism,
  LucanianCattleBreedOrganism: AnimalBreeds_LucanianCattleBreedOrganism,
  MaremmanaCattleBreedOrganism: AnimalBreeds_MaremmanaCattleBreedOrganism,
  MarianasCattleBreedOrganism: AnimalBreeds_MarianasCattleBreedOrganism,
  MaryutiCattleBreedOrganism: AnimalBreeds_MaryutiCattleBreedOrganism,
  MauritiusCroleCattleBreedOrganism: AnimalBreeds_MauritiusCroleCattleBreedOrganism,
  MenufiCattleBreedOrganism: AnimalBreeds_MenufiCattleBreedOrganism,
  MezzalinaCattleBreedOrganism: AnimalBreeds_MezzalinaCattleBreedOrganism,
  ModicanaCattleBreedOrganism: AnimalBreeds_ModicanaCattleBreedOrganism,
  MoiCattleBreedOrganism: AnimalBreeds_MoiCattleBreedOrganism,
  NamaCattleBreedOrganism: AnimalBreeds_NamaCattleBreedOrganism,
  NanyangCattleBreedOrganism: AnimalBreeds_NanyangCattleBreedOrganism,
  NQuoteDamaSangaCattleBreedOrganism: AnimalBreeds_NQuoteDamaSangaCattleBreedOrganism,
  NgandaCattleBreedOrganism: AnimalBreeds_NgandaCattleBreedOrganism,
  NiloticSangaCattleBreedOrganism: AnimalBreeds_NiloticSangaCattleBreedOrganism,
  NkoneCattleBreedOrganism: AnimalBreeds_NkoneCattleBreedOrganism,
  NorthMalawiAngoniCattleBreedOrganism: AnimalBreeds_NorthMalawiAngoniCattleBreedOrganism,
  NuerCattleBreedOrganism: AnimalBreeds_NuerCattleBreedOrganism,
  NurasCattleBreedOrganism: AnimalBreeds_NurasCattleBreedOrganism,
  NyoroCattleBreedOrganism: AnimalBreeds_NyoroCattleBreedOrganism,
  OvamboCattleBreedOrganism: AnimalBreeds_OvamboCattleBreedOrganism,
  PantelleriaCattleBreedOrganism: AnimalBreeds_PantelleriaCattleBreedOrganism,
  PinzhouCattleBreedOrganism: AnimalBreeds_PinzhouCattleBreedOrganism,
  PortoAmboimCattleBreedOrganism: AnimalBreeds_PortoAmboimCattleBreedOrganism,
  PosavinaCattleBreedOrganism: AnimalBreeds_PosavinaCattleBreedOrganism,
  RomanianSteppeCattleBreedOrganism: AnimalBreeds_RomanianSteppeCattleBreedOrganism,
  SaidiCattleBreedOrganism: AnimalBreeds_SaidiCattleBreedOrganism,
  SardoModicanaCattleBreedOrganism: AnimalBreeds_SardoModicanaCattleBreedOrganism,
  SengologaCattleBreedOrganism: AnimalBreeds_SengologaCattleBreedOrganism,
  SerereCattleBreedOrganism: AnimalBreeds_SerereCattleBreedOrganism,
  SeshagaCattleBreedOrganism: AnimalBreeds_SeshagaCattleBreedOrganism,
  SiberianBlackPiedCattleBreedOrganism: AnimalBreeds_SiberianBlackPiedCattleBreedOrganism,
  SocotraCattleBreedOrganism: AnimalBreeds_SocotraCattleBreedOrganism,
  SouthernTswanaCattleBreedOrganism: AnimalBreeds_SouthernTswanaCattleBreedOrganism,
  SprecaCattleBreedOrganism: AnimalBreeds_SprecaCattleBreedOrganism,
  SunkumaCattleBreedOrganism: AnimalBreeds_SunkumaCattleBreedOrganism,
  TaiwanZebuCattleBreedOrganism: AnimalBreeds_TaiwanZebuCattleBreedOrganism,
  ThaiCattleBreedOrganism: AnimalBreeds_ThaiCattleBreedOrganism,
  ThailandFightingZebuCattleBreedOrganism: AnimalBreeds_ThailandFightingZebuCattleBreedOrganism,
  ThanhHoaCattleBreedOrganism: AnimalBreeds_ThanhHoaCattleBreedOrganism,
  TibetanCattleBreedOrganism: AnimalBreeds_TibetanCattleBreedOrganism,
  TongaCattleBreedOrganism: AnimalBreeds_TongaCattleBreedOrganism,
  ToroCattleBreedOrganism: AnimalBreeds_ToroCattleBreedOrganism,
  TuniCattleBreedOrganism: AnimalBreeds_TuniCattleBreedOrganism,
  TurkishGraySteppeCattleBreedOrganism: AnimalBreeds_TurkishGraySteppeCattleBreedOrganism,
  TuyHoaCattleBreedOrganism: AnimalBreeds_TuyHoaCattleBreedOrganism,
  UjumqinCattleBreedOrganism: AnimalBreeds_UjumqinCattleBreedOrganism,
  AbigarCattleBreedOrganism: AnimalBreeds_AbigarCattleBreedOrganism,
  AfricangusCattleBreedOrganism: AnimalBreeds_AfricangusCattleBreedOrganism,
  AgeroleseCattleBreedOrganism: AnimalBreeds_AgeroleseCattleBreedOrganism,
  AlbeseCattleBreedOrganism: AnimalBreeds_AlbeseCattleBreedOrganism,
  UkrainianGrayCattleBreedOrganism: AnimalBreeds_UkrainianGrayCattleBreedOrganism,
  VietnameseYellowCattleBreedOrganism: AnimalBreeds_VietnameseYellowCattleBreedOrganism,
  WatusiUnitedStatesOfAmericaCattleBreedOrganism: AnimalBreeds_WatusiUnitedStatesOfAmericaCattleBreedOrganism,
  WenshanCattleBreedOrganism: AnimalBreeds_WenshanCattleBreedOrganism,
  YakutCattleBreedOrganism: AnimalBreeds_YakutCattleBreedOrganism,
  YunnanZebuCattleBreedOrganism: AnimalBreeds_YunnanZebuCattleBreedOrganism,
  ZambiaAngoniCattleBreedOrganism: AnimalBreeds_ZambiaAngoniCattleBreedOrganism,
  DrakensbergerCattleBreedOrganism: AnimalBreeds_DrakensbergerCattleBreedOrganism,
  ModicanaLowlandCattleBreedOrganism: AnimalBreeds_ModicanaLowlandCattleBreedOrganism,
  TaiwanYellowCattleBreedOrganism: AnimalBreeds_TaiwanYellowCattleBreedOrganism,
  MengguCattleBreedOrganism: AnimalBreeds_MengguCattleBreedOrganism,
  AlbresCattleBreedOrganism: AnimalBreeds_AlbresCattleBreedOrganism,
  AlentejanaCattleBreedOrganism: AnimalBreeds_AlentejanaCattleBreedOrganism,
  AmericanWhiteParkCattleBreedOrganism: AnimalBreeds_AmericanWhiteParkCattleBreedOrganism,
  AmerifaxcattleBreedOrganism: AnimalBreeds_AmerifaxcattleBreedOrganism,
  AnatolianBlackCattleBreedOrganism: AnimalBreeds_AnatolianBlackCattleBreedOrganism,
  AndalusianBlackCattleBreedOrganism: AnimalBreeds_AndalusianBlackCattleBreedOrganism,
  SpanishChickenOrganism: AnimalBreeds_SpanishChickenOrganism,
  AndalusianGrayCattleBreedOrganism: AnimalBreeds_AndalusianGrayCattleBreedOrganism,
  AngelnCattleBreedOrganism: AnimalBreeds_AngelnCattleBreedOrganism,
  AsturianMountainCattleBreedOrganism: AnimalBreeds_AsturianMountainCattleBreedOrganism,
  AsturianValleyCattleBreedOrganism: AnimalBreeds_AsturianValleyCattleBreedOrganism,
  AubracCattleBreedOrganism: AnimalBreeds_AubracCattleBreedOrganism,
  AulieAtaCattleBreedOrganism: AnimalBreeds_AulieAtaCattleBreedOrganism,
  AustralianLowlineCattleBreedOrganism: AnimalBreeds_AustralianLowlineCattleBreedOrganism,
  BarzonaCattleBreedOrganism: AnimalBreeds_BarzonaCattleBreedOrganism,
  BazadaisCattleBreedOrganism: AnimalBreeds_BazadaisCattleBreedOrganism,
  BeefmakerCattleBreedOrganism: AnimalBreeds_BeefmakerCattleBreedOrganism,
  BelarusRedCattleBreedOrganism: AnimalBreeds_BelarusRedCattleBreedOrganism,
  BelgianBlueCattleBreedOrganism: AnimalBreeds_BelgianBlueCattleBreedOrganism,
  BelgianRedCattleBreedOrganism: AnimalBreeds_BelgianRedCattleBreedOrganism,
  BelmontAdaptaurCattleBreedOrganism: AnimalBreeds_BelmontAdaptaurCattleBreedOrganism,
  BerrendasCattleBreedOrganism: AnimalBreeds_BerrendasCattleBreedOrganism,
  BlacksidedTrondheimAndNorlandCattleBreedOrganism: AnimalBreeds_BlacksidedTrondheimAndNorlandCattleBreedOrganism,
  BlancoOrejinegroCattleBreedOrganism: AnimalBreeds_BlancoOrejinegroCattleBreedOrganism,
  BraunviehCattleBreedOrganism: AnimalBreeds_BraunviehCattleBreedOrganism,
  BritishWhiteCattleBreedOrganism: AnimalBreeds_BritishWhiteCattleBreedOrganism,
  CachenaCattleBreedOrganism: AnimalBreeds_CachenaCattleBreedOrganism,
  CanaryIslandCattleBreedOrganism: AnimalBreeds_CanaryIslandCattleBreedOrganism,
  CarinthianBlondCattleBreedOrganism: AnimalBreeds_CarinthianBlondCattleBreedOrganism,
  CaucasianCattleBreedOrganism: AnimalBreeds_CaucasianCattleBreedOrganism,
  CharolaisCattleBreedOrganism: AnimalBreeds_CharolaisCattleBreedOrganism,
  ChineseBlackAndWhiteCattleBreedOrganism: AnimalBreeds_ChineseBlackAndWhiteCattleBreedOrganism,
  CorrienteCattleBreedOrganism: AnimalBreeds_CorrienteCattleBreedOrganism,
  CosteoConCuernosCattleBreedOrganism: AnimalBreeds_CosteoConCuernosCattleBreedOrganism,
  DamascusCattleBreedOrganism: AnimalBreeds_DamascusCattleBreedOrganism,
  DanishRedCattleBreedOrganism: AnimalBreeds_DanishRedCattleBreedOrganism,
  DevonCattleBreedOrganism: AnimalBreeds_DevonCattleBreedOrganism,
  DlafeCattleBreedOrganism: AnimalBreeds_DlafeCattleBreedOrganism,
  DutchBeltedCattleBreedOrganism: AnimalBreeds_DutchBeltedCattleBreedOrganism,
  DutchFriesianCattleBreedOrganism: AnimalBreeds_DutchFriesianCattleBreedOrganism,
  EnglishLonghornCattleBreedOrganism: AnimalBreeds_EnglishLonghornCattleBreedOrganism,
  EstonianRedCattleBreedOrganism: AnimalBreeds_EstonianRedCattleBreedOrganism,
  EvolneCattleBreedOrganism: AnimalBreeds_EvolneCattleBreedOrganism,
  FightingBullCattleBreedOrganism: AnimalBreeds_FightingBullCattleBreedOrganism,
  FjallCattleBreedOrganism: AnimalBreeds_FjallCattleBreedOrganism,
  FloridaCrackerPineywoodsCattleBreedOrganism: AnimalBreeds_FloridaCrackerPineywoodsCattleBreedOrganism,
  GalicianBlondCattleBreedOrganism: AnimalBreeds_GalicianBlondCattleBreedOrganism,
  GasconCattleBreedOrganism: AnimalBreeds_GasconCattleBreedOrganism,
  GermanRedPiedCattleBreedOrganism: AnimalBreeds_GermanRedPiedCattleBreedOrganism,
  GlanCattleBreedOrganism: AnimalBreeds_GlanCattleBreedOrganism,
  GloucesterCattleBreedOrganism: AnimalBreeds_GloucesterCattleBreedOrganism,
  GroningenWhiteheadedCattleBreedOrganism: AnimalBreeds_GroningenWhiteheadedCattleBreedOrganism,
  HartnCattleBreedOrganism: AnimalBreeds_HartnCattleBreedOrganism,
  MixedBreedGoatOrganism: AnimalBreeds_MixedBreedGoatOrganism,
  AustralianGoatBreedOrganism: AnimalBreeds_AustralianGoatBreedOrganism,
  ArapawaIslandGoatBreedOrganism: AnimalBreeds_ArapawaIslandGoatBreedOrganism,
  MalteseGoatBreedOrganism: AnimalBreeds_MalteseGoatBreedOrganism,
  ProvenaleGoatBreedOrganism: AnimalBreeds_ProvenaleGoatBreedOrganism,
  NegraSerranaGoatBreedOrganism: AnimalBreeds_NegraSerranaGoatBreedOrganism,
  OrobicaGoatBreedOrganism: AnimalBreeds_OrobicaGoatBreedOrganism,
  RoyaVesubieGoatBreedOrganism: AnimalBreeds_RoyaVesubieGoatBreedOrganism,
  RetintaExtremenaGoatBreedOrganism: AnimalBreeds_RetintaExtremenaGoatBreedOrganism,
  AppenzellGoatBreedOrganism: AnimalBreeds_AppenzellGoatBreedOrganism,
  AmericanCashmereGoatBreedOrganism: AnimalBreeds_AmericanCashmereGoatBreedOrganism,
  AltaiMountainGoatBreedOrganism: AnimalBreeds_AltaiMountainGoatBreedOrganism,
  PyreneanGoatBreedOrganism: AnimalBreeds_PyreneanGoatBreedOrganism,
  BagotGoatBreedOrganism: AnimalBreeds_BagotGoatBreedOrganism,
  RussianWhiteGoatBreedOrganism: AnimalBreeds_RussianWhiteGoatBreedOrganism,
  MoxotGoatBreedOrganism: AnimalBreeds_MoxotGoatBreedOrganism,
  MyotonicGoatBreedOrganism: AnimalBreeds_MyotonicGoatBreedOrganism,
  NachiGoatBreedOrganism: AnimalBreeds_NachiGoatBreedOrganism,
  NigerianDwarfGoatBreedOrganism: AnimalBreeds_NigerianDwarfGoatBreedOrganism,
  SardaGoatBreedOrganism: AnimalBreeds_SardaGoatBreedOrganism,
  SerpentinaGoatBreedOrganism: AnimalBreeds_SerpentinaGoatBreedOrganism,
  SerranaGoatBreedOrganism: AnimalBreeds_SerranaGoatBreedOrganism,
  VerataGoatBreedOrganism: AnimalBreeds_VerataGoatBreedOrganism,
  VerzascaBlackGoatBreedOrganism: AnimalBreeds_VerzascaBlackGoatBreedOrganism,
  NorwegianGoatBreedOrganism: AnimalBreeds_NorwegianGoatBreedOrganism,
  OberhasliGoatBreedOrganism: AnimalBreeds_OberhasliGoatBreedOrganism,
  PeacockGoatBreedOrganism: AnimalBreeds_PeacockGoatBreedOrganism,
  PhilippineGoatBreedOrganism: AnimalBreeds_PhilippineGoatBreedOrganism,
  LoashanGoatBreedOrganism: AnimalBreeds_LoashanGoatBreedOrganism,
  SanClementeGoatBreedOrganism: AnimalBreeds_SanClementeGoatBreedOrganism,
  SomaliGoatBreedOrganism: AnimalBreeds_SomaliGoatBreedOrganism,
  SpanishGoatBreedOrganism: AnimalBreeds_SpanishGoatBreedOrganism,
  RoveGoatBreedOrganism: AnimalBreeds_RoveGoatBreedOrganism,
  SRDGoatBreedOrganism: AnimalBreeds_SRDGoatBreedOrganism,
  SwedishLandraceGoatBreedOrganism: AnimalBreeds_SwedishLandraceGoatBreedOrganism,
  ThuringianGoatBreedOrganism: AnimalBreeds_ThuringianGoatBreedOrganism,
  UzbekBlackGoatBreedOrganism: AnimalBreeds_UzbekBlackGoatBreedOrganism,
  ZhongweiGoatBreedOrganism: AnimalBreeds_ZhongweiGoatBreedOrganism,
  BarbariGoatBreedOrganism: AnimalBreeds_BarbariGoatBreedOrganism,
  PoitouGoatBreedOrganism: AnimalBreeds_PoitouGoatBreedOrganism,
  RepartidaGoatBreedOrganism: AnimalBreeds_RepartidaGoatBreedOrganism,
  BootedGoatBreedOrganism: AnimalBreeds_BootedGoatBreedOrganism,
  CorsicanGoatBreedOrganism: AnimalBreeds_CorsicanGoatBreedOrganism,
  ChaparGoatBreedOrganism: AnimalBreeds_ChaparGoatBreedOrganism,
  CanindGoatBreedOrganism: AnimalBreeds_CanindGoatBreedOrganism,
  CanaryIslandGoatBreedOrganism: AnimalBreeds_CanaryIslandGoatBreedOrganism,
  DaeraDinPanahGoatBreedOrganism: AnimalBreeds_DaeraDinPanahGoatBreedOrganism,
  BritishAlpineGoatBreedOrganism: AnimalBreeds_BritishAlpineGoatBreedOrganism,
  BhujGoatBreedOrganism: AnimalBreeds_BhujGoatBreedOrganism,
  BoerGoatBreedOrganism: AnimalBreeds_BoerGoatBreedOrganism,
  BenadirGoatBreedOrganism: AnimalBreeds_BenadirGoatBreedOrganism,
  CroleAntillesGoatBreedOrganism: AnimalBreeds_CroleAntillesGoatBreedOrganism,
  BeetalGoatBreedOrganism: AnimalBreeds_BeetalGoatBreedOrganism,
  GoldenGuernseyGoatBreedOrganism: AnimalBreeds_GoldenGuernseyGoatBreedOrganism,
  DanishLandraceGoatBreedOrganism: AnimalBreeds_DanishLandraceGoatBreedOrganism,
  KaghaniGoatBreedOrganism: AnimalBreeds_KaghaniGoatBreedOrganism,
  IrishGoatBreedOrganism: AnimalBreeds_IrishGoatBreedOrganism,
  GrisonsStripedGoatBreedOrganism: AnimalBreeds_GrisonsStripedGoatBreedOrganism,
  JiningGrayGoatBreedOrganism: AnimalBreeds_JiningGrayGoatBreedOrganism,
  FinnishLandraceGoatBreedOrganism: AnimalBreeds_FinnishLandraceGoatBreedOrganism,
  ErzgebirgGoatBreedOrganism: AnimalBreeds_ErzgebirgGoatBreedOrganism,
  KamoriGoatBreedOrganism: AnimalBreeds_KamoriGoatBreedOrganism,
  DonGoatBreedOrganism: AnimalBreeds_DonGoatBreedOrganism,
  KikoGoatBreedOrganism: AnimalBreeds_KikoGoatBreedOrganism,
  KinderGoatBreedOrganism: AnimalBreeds_KinderGoatBreedOrganism,
  PygoraGoatBreedOrganism: AnimalBreeds_PygoraGoatBreedOrganism,
  WoodenLegGoatBreedOrganism: AnimalBreeds_WoodenLegGoatBreedOrganism,
  AlpineChamoiseeGoatBreedOrganism: AnimalBreeds_AlpineChamoiseeGoatBreedOrganism,
  MassifCentralGoatBreedOrganism: AnimalBreeds_MassifCentralGoatBreedOrganism,
  MalagueaGoatBreedOrganism: AnimalBreeds_MalagueaGoatBreedOrganism,
  AlgarviaGoatBreedOrganism: AnimalBreeds_AlgarviaGoatBreedOrganism,
  BritishSaanenGoatBreedOrganism: AnimalBreeds_BritishSaanenGoatBreedOrganism,
  BritishToggenburgGoatBreedOrganism: AnimalBreeds_BritishToggenburgGoatBreedOrganism,
  BndnerGoatBreedOrganism: AnimalBreeds_BndnerGoatBreedOrganism,
  BlancaAndaluzaGoatBreedOrganism: AnimalBreeds_BlancaAndaluzaGoatBreedOrganism,
  BlancaCeltibericaGoatBreedOrganism: AnimalBreeds_BlancaCeltibericaGoatBreedOrganism,
  BraviaGoatBreedOrganism: AnimalBreeds_BraviaGoatBreedOrganism,
  BlackGrisonneGoatBreedOrganism: AnimalBreeds_BlackGrisonneGoatBreedOrganism,
  ChamoisOfTheAlpsGoatBreedOrganism: AnimalBreeds_ChamoisOfTheAlpsGoatBreedOrganism,
  CharnequeriaGoatBreedOrganism: AnimalBreeds_CharnequeriaGoatBreedOrganism,
  CarpatheGoatBreedOrganism: AnimalBreeds_CarpatheGoatBreedOrganism,
  ColNoirDuValaisGoatBreedOrganism: AnimalBreeds_ColNoirDuValaisGoatBreedOrganism,
  DamaniGoatBreedOrganism: AnimalBreeds_DamaniGoatBreedOrganism,
  DesFossesCommunesDeLQuoteOuestGoatBreedOrganism: AnimalBreeds_DesFossesCommunesDeLQuoteOuestGoatBreedOrganism,
  EnglishGoatBreedOrganism: AnimalBreeds_EnglishGoatBreedOrganism,
  EnglishGuernseyGoatBreedOrganism: AnimalBreeds_EnglishGuernseyGoatBreedOrganism,
  GermanColoredGoatBreedOrganism: AnimalBreeds_GermanColoredGoatBreedOrganism,
  GuadarramaGoatBreedOrganism: AnimalBreeds_GuadarramaGoatBreedOrganism,
  GarganicaGoatBreedOrganism: AnimalBreeds_GarganicaGoatBreedOrganism,
  GirgentanaGoatBreedOrganism: AnimalBreeds_GirgentanaGoatBreedOrganism,
  JonicaGoatBreedOrganism: AnimalBreeds_JonicaGoatBreedOrganism,
  MurcianaGranadinaGoatBreedOrganism: AnimalBreeds_MurcianaGranadinaGoatBreedOrganism,
  BndnerOberlandSheepBreedOrganism: AnimalBreeds_BndnerOberlandSheepBreedOrganism,
  BritishMilkSheepBreedOrganism: AnimalBreeds_BritishMilkSheepBreedOrganism,
  BrillenschafSheepBreedOrganism: AnimalBreeds_BrillenschafSheepBreedOrganism,
  BrecknockHillCheviotSheepBreedOrganism: AnimalBreeds_BrecknockHillCheviotSheepBreedOrganism,
  CholistaniSheepBreedOrganism: AnimalBreeds_CholistaniSheepBreedOrganism,
  BibrikSheepBreedOrganism: AnimalBreeds_BibrikSheepBreedOrganism,
  ColumbiaSheepBreedOrganism: AnimalBreeds_ColumbiaSheepBreedOrganism,
  BlackWelshMountainSheepBreedOrganism: AnimalBreeds_BlackWelshMountainSheepBreedOrganism,
  BlackheadPersianSheepBreedOrganism: AnimalBreeds_BlackheadPersianSheepBreedOrganism,
  BleuDuMaineSheepBreedOrganism: AnimalBreeds_BleuDuMaineSheepBreedOrganism,
  BluefacedLeicesterSheepBreedOrganism: AnimalBreeds_BluefacedLeicesterSheepBreedOrganism,
  BondSheepBreedOrganism: AnimalBreeds_BondSheepBreedOrganism,
  BorderLeicesterSheepBreedOrganism: AnimalBreeds_BorderLeicesterSheepBreedOrganism,
  BoreraySheepBreedOrganism: AnimalBreeds_BoreraySheepBreedOrganism,
  BovskaSheepBreedOrganism: AnimalBreeds_BovskaSheepBreedOrganism,
  BraunesBergschafSheepBreedOrganism: AnimalBreeds_BraunesBergschafSheepBreedOrganism,
  BrazilianSomaliSheepBreedOrganism: AnimalBreeds_BrazilianSomaliSheepBreedOrganism,
  BeulahSpeckledFaceSheepBreedOrganism: AnimalBreeds_BeulahSpeckledFaceSheepBreedOrganism,
  DartmoorSheepBreedOrganism: AnimalBreeds_DartmoorSheepBreedOrganism,
  FabrianeseSheepBreedOrganism: AnimalBreeds_FabrianeseSheepBreedOrganism,
  ExmoorHornSheepBreedOrganism: AnimalBreeds_ExmoorHornSheepBreedOrganism,
  ElliottdaleSheepBreedOrganism: AnimalBreeds_ElliottdaleSheepBreedOrganism,
  DrysdaleSheepBreedOrganism: AnimalBreeds_DrysdaleSheepBreedOrganism,
  DorsetDownSheepBreedOrganism: AnimalBreeds_DorsetDownSheepBreedOrganism,
  GermanBlackheadedMuttonSheepBreedOrganism: AnimalBreeds_GermanBlackheadedMuttonSheepBreedOrganism,
  KookaSheepBreedOrganism: AnimalBreeds_KookaSheepBreedOrganism,
  FriesianMilkSheepBreedOrganism: AnimalBreeds_FriesianMilkSheepBreedOrganism,
  GansuAlpineFineWoolSheepBreedOrganism: AnimalBreeds_GansuAlpineFineWoolSheepBreedOrganism,
  GermanWhiteheadedMuttonSheepBreedOrganism: AnimalBreeds_GermanWhiteheadedMuttonSheepBreedOrganism,
  GraueGehoernteHeidschnuckeSheepBreedOrganism: AnimalBreeds_GraueGehoernteHeidschnuckeSheepBreedOrganism,
  HanSheepBreedOrganism: AnimalBreeds_HanSheepBreedOrganism,
  GromarkSheepBreedOrganism: AnimalBreeds_GromarkSheepBreedOrganism,
  GulfCoastNativeSheepBreedOrganism: AnimalBreeds_GulfCoastNativeSheepBreedOrganism,
  DorperSheepBreedOrganism: AnimalBreeds_DorperSheepBreedOrganism,
  DevonClosewoolSheepBreedOrganism: AnimalBreeds_DevonClosewoolSheepBreedOrganism,
  DeutschesBlaukoepfigesFleischschafSheepBreedOrganism: AnimalBreeds_DeutschesBlaukoepfigesFleischschafSheepBreedOrganism,
  DerbyshireGritstoneSheepBreedOrganism: AnimalBreeds_DerbyshireGritstoneSheepBreedOrganism,
  CoburgerFuchsschafSheepBreedOrganism: AnimalBreeds_CoburgerFuchsschafSheepBreedOrganism,
  DanishLandraceSheepBreedOrganism: AnimalBreeds_DanishLandraceSheepBreedOrganism,
  GuteSheepBreedOrganism: AnimalBreeds_GuteSheepBreedOrganism,
  HampshireSheepBreedOrganism: AnimalBreeds_HampshireSheepBreedOrganism,
  GentileDiPugliaSheepBreedOrganism: AnimalBreeds_GentileDiPugliaSheepBreedOrganism,
  GermanMountainSheepBreedOrganism: AnimalBreeds_GermanMountainSheepBreedOrganism,
  LuzeinSheepBreedOrganism: AnimalBreeds_LuzeinSheepBreedOrganism,
  KatahdinSheepBreedOrganism: AnimalBreeds_KatahdinSheepBreedOrganism,
  LeineschafSheepBreedOrganism: AnimalBreeds_LeineschafSheepBreedOrganism,
  LincolnLongwoolSheepBreedOrganism: AnimalBreeds_LincolnLongwoolSheepBreedOrganism,
  LlanwenogSheepBreedOrganism: AnimalBreeds_LlanwenogSheepBreedOrganism,
  LleynSheepBreedOrganism: AnimalBreeds_LleynSheepBreedOrganism,
  DamaraSheepBreedOrganism: AnimalBreeds_DamaraSheepBreedOrganism,
  DamaniSheepBreedOrganism: AnimalBreeds_DamaniSheepBreedOrganism,
  DalesbredSheepBreedOrganism: AnimalBreeds_DalesbredSheepBreedOrganism,
  DalaSheepBreedOrganism: AnimalBreeds_DalaSheepBreedOrganism,
  CriolloSheepBreedOrganism: AnimalBreeds_CriolloSheepBreedOrganism,
  CormoSheepBreedOrganism: AnimalBreeds_CormoSheepBreedOrganism,
  LatiSheepBreedOrganism: AnimalBreeds_LatiSheepBreedOrganism,
  LonkSheepBreedOrganism: AnimalBreeds_LonkSheepBreedOrganism,
  LangheSheepBreedOrganism: AnimalBreeds_LangheSheepBreedOrganism,
  ManxLoaghtanSheepBreedOrganism: AnimalBreeds_ManxLoaghtanSheepBreedOrganism,
  MasaiSheepBreedOrganism: AnimalBreeds_MasaiSheepBreedOrganism,
  MerinolandschafSheepBreedOrganism: AnimalBreeds_MerinolandschafSheepBreedOrganism,
  LohiSheepBreedOrganism: AnimalBreeds_LohiSheepBreedOrganism,
  IleDeFranceSheepBreedOrganism: AnimalBreeds_IleDeFranceSheepBreedOrganism,
  HashtNagriSheepBreedOrganism: AnimalBreeds_HashtNagriSheepBreedOrganism,
  HazaragieSheepBreedOrganism: AnimalBreeds_HazaragieSheepBreedOrganism,
  CoopworthSheepBreedOrganism: AnimalBreeds_CoopworthSheepBreedOrganism,
  ComisanaSheepBreedOrganism: AnimalBreeds_ComisanaSheepBreedOrganism,
  ComebackSheepBreedOrganism: AnimalBreeds_ComebackSheepBreedOrganism,
  SicilianBarbarySheepBreedOrganism: AnimalBreeds_SicilianBarbarySheepBreedOrganism,
  AfricanaSheepBreedOrganism: AnimalBreeds_AfricanaSheepBreedOrganism,
  WelshMountainBadgerFacedSheepBreedOrganism: AnimalBreeds_WelshMountainBadgerFacedSheepBreedOrganism,
  HebrideanSheepBreedOrganism: AnimalBreeds_HebrideanSheepBreedOrganism,
  HeidschnuckeSheepBreedOrganism: AnimalBreeds_HeidschnuckeSheepBreedOrganism,
  HerdwickSheepBreedOrganism: AnimalBreeds_HerdwickSheepBreedOrganism,
  HillRadnorSheepBreedOrganism: AnimalBreeds_HillRadnorSheepBreedOrganism,
  IcelandicSheepBreedOrganism: AnimalBreeds_IcelandicSheepBreedOrganism,
  HarnaiSheepBreedOrganism: AnimalBreeds_HarnaiSheepBreedOrganism,
  IstrianPramenkaSheepBreedOrganism: AnimalBreeds_IstrianPramenkaSheepBreedOrganism,
  JacobSheepBreedOrganism: AnimalBreeds_JacobSheepBreedOrganism,
  JezerskosolcavskaSheepBreedOrganism: AnimalBreeds_JezerskosolcavskaSheepBreedOrganism,
  KachhiSheepBreedOrganism: AnimalBreeds_KachhiSheepBreedOrganism,
  WensleydaleSheepBreedOrganism: AnimalBreeds_WensleydaleSheepBreedOrganism,
  WestAfricanDwarfSheepBreedOrganism: AnimalBreeds_WestAfricanDwarfSheepBreedOrganism,
  WhiteSuffolkSheepBreedOrganism: AnimalBreeds_WhiteSuffolkSheepBreedOrganism,
  WhitefaceDartmoorSheepBreedOrganism: AnimalBreeds_WhitefaceDartmoorSheepBreedOrganism,
  WhitefaceWoodlandSheepBreedOrganism: AnimalBreeds_WhitefaceWoodlandSheepBreedOrganism,
  XinjiangFinewoolSheepBreedOrganism: AnimalBreeds_XinjiangFinewoolSheepBreedOrganism,
  KajliSheepBreedOrganism: AnimalBreeds_KajliSheepBreedOrganism,
  HogIslandSheepBreedOrganism: AnimalBreeds_HogIslandSheepBreedOrganism,
  BielleseSheepBreedOrganism: AnimalBreeds_BielleseSheepBreedOrganism,
  ChiosSheepBreedOrganism: AnimalBreeds_ChiosSheepBreedOrganism,
  SantaCruzSheepBreedOrganism: AnimalBreeds_SantaCruzSheepBreedOrganism,
  CharollaisSheepBreedOrganism: AnimalBreeds_CharollaisSheepBreedOrganism,
  CastlemilkMooritSheepBreedOrganism: AnimalBreeds_CastlemilkMooritSheepBreedOrganism,
  CampanianBarbarySheepBreedOrganism: AnimalBreeds_CampanianBarbarySheepBreedOrganism,
  CaliforniaVariegatedMutantSheepBreedOrganism: AnimalBreeds_CaliforniaVariegatedMutantSheepBreedOrganism,
  CaliforniaRedSheepBreedOrganism: AnimalBreeds_CaliforniaRedSheepBreedOrganism,
  SopravissanaSheepBreedOrganism: AnimalBreeds_SopravissanaSheepBreedOrganism,
  SomaliSheepBreedOrganism: AnimalBreeds_SomaliSheepBreedOrganism,
  WelshHillSpeckledFaceSheepBreedOrganism: AnimalBreeds_WelshHillSpeckledFaceSheepBreedOrganism,
  SkuddeSheepBreedOrganism: AnimalBreeds_SkuddeSheepBreedOrganism,
  WaziriSheepBreedOrganism: AnimalBreeds_WaziriSheepBreedOrganism,
  ShetlandSheepBreedOrganism: AnimalBreeds_ShetlandSheepBreedOrganism,
  CambridgeSheepBreedOrganism: AnimalBreeds_CambridgeSheepBreedOrganism,
  SolognoteSheepBreedOrganism: AnimalBreeds_SolognoteSheepBreedOrganism,
  ColombianCriolloHorseBreedOrganism: AnimalBreeds_ColombianCriolloHorseBreedOrganism,
  ComtoisHorseBreedOrganism: AnimalBreeds_ComtoisHorseBreedOrganism,
  CorsicanHorseBreedOrganism: AnimalBreeds_CorsicanHorseBreedOrganism,
  CostaRicanSaddleHorseHorseBreedOrganism: AnimalBreeds_CostaRicanSaddleHorseHorseBreedOrganism,
  CostenoHorseBreedOrganism: AnimalBreeds_CostenoHorseBreedOrganism,
  CubanPasoHorseBreedOrganism: AnimalBreeds_CubanPasoHorseBreedOrganism,
  CayugaBreedDuckOrganism: AnimalBreeds_CayugaBreedDuckOrganism,
  RunnerBreedDuckOrganism: AnimalBreeds_RunnerBreedDuckOrganism,
  CallBreedDuckOrganism: AnimalBreeds_CallBreedDuckOrganism,
  OrpingtonBreedDuckOrganism: AnimalBreeds_OrpingtonBreedDuckOrganism,
  CrestedBreedDuckOrganism: AnimalBreeds_CrestedBreedDuckOrganism,
  PommeranianBreedDuckOrganism: AnimalBreeds_PommeranianBreedDuckOrganism,
  RoughFellSheepBreedOrganism: AnimalBreeds_RoughFellSheepBreedOrganism,
  BlueSwedishBreedDuckOrganism: AnimalBreeds_BlueSwedishBreedDuckOrganism,
  Golden300HybridBreedDuckOrganism: AnimalBreeds_Golden300HybridBreedDuckOrganism,
  DanishWarmbloodHorseBreedOrganism: AnimalBreeds_DanishWarmbloodHorseBreedOrganism,
  AnconaBreedDuckOrganism: AnimalBreeds_AnconaBreedDuckOrganism,
  AustralianSpottedBreedDuckOrganism: AnimalBreeds_AustralianSpottedBreedDuckOrganism,
  SwaledaleSheepBreedOrganism: AnimalBreeds_SwaledaleSheepBreedOrganism,
  PolypaySheepBreedOrganism: AnimalBreeds_PolypaySheepBreedOrganism,
  AylesburyBreedDuckOrganism: AnimalBreeds_AylesburyBreedDuckOrganism,
  BlackEastIndiesBreedDuckOrganism: AnimalBreeds_BlackEastIndiesBreedDuckOrganism,
  CoastBreedDuckOrganism: AnimalBreeds_CoastBreedDuckOrganism,
  KromsnaveleendBreedDuckOrganism: AnimalBreeds_KromsnaveleendBreedDuckOrganism,
  MagpieBreedDuckOrganism: AnimalBreeds_MagpieBreedDuckOrganism,
  SilverAppleyardBreedDuckOrganism: AnimalBreeds_SilverAppleyardBreedDuckOrganism,
  PagliarolaSheepBreedOrganism: AnimalBreeds_PagliarolaSheepBreedOrganism,
  PomeranianCoarsewoolSheepBreedOrganism: AnimalBreeds_PomeranianCoarsewoolSheepBreedOrganism,
  SheepBreedUndeterminedSheepBreedOrganism: AnimalBreeds_SheepBreedUndeterminedSheepBreedOrganism,
  OrkneySheepBreedOrganism: AnimalBreeds_OrkneySheepBreedOrganism,
  OldNorwegianSheepBreedOrganism: AnimalBreeds_OldNorwegianSheepBreedOrganism,
  OldFormatSheepBreedOrganism: AnimalBreeds_OldFormatSheepBreedOrganism,
  NorwegianFurSheepBreedOrganism: AnimalBreeds_NorwegianFurSheepBreedOrganism,
  NorfolkHornSheepBreedOrganism: AnimalBreeds_NorfolkHornSheepBreedOrganism,
  NavajoChurroSheepBreedOrganism: AnimalBreeds_NavajoChurroSheepBreedOrganism,
  SwedishYellowBreedDuckOrganism: AnimalBreeds_SwedishYellowBreedDuckOrganism,
  DiepholzGooseBreedOrganism: AnimalBreeds_DiepholzGooseBreedOrganism,
  AfricanGooseBreedOrganism: AnimalBreeds_AfricanGooseBreedOrganism,
  EmbdenGooseBreedOrganism: AnimalBreeds_EmbdenGooseBreedOrganism,
  ChineseWhiteGooseBreedOrganism: AnimalBreeds_ChineseWhiteGooseBreedOrganism,
  ChineseBrownGooseBreedOrganism: AnimalBreeds_ChineseBrownGooseBreedOrganism,
  ToulouseGooseBreedOrganism: AnimalBreeds_ToulouseGooseBreedOrganism,
  AmericanBuffGooseBreedOrganism: AnimalBreeds_AmericanBuffGooseBreedOrganism,
  PomeranianGooseBreedOrganism: AnimalBreeds_PomeranianGooseBreedOrganism,
  RomanGooseBreedOrganism: AnimalBreeds_RomanGooseBreedOrganism,
  ScaniaGooseBreedOrganism: AnimalBreeds_ScaniaGooseBreedOrganism,
  RackaSheepBreedOrganism: AnimalBreeds_RackaSheepBreedOrganism,
  RasaAragonesaSheepBreedOrganism: AnimalBreeds_RasaAragonesaSheepBreedOrganism,
  RedEngadineSheepBreedOrganism: AnimalBreeds_RedEngadineSheepBreedOrganism,
  RhoenschafSheepBreedOrganism: AnimalBreeds_RhoenschafSheepBreedOrganism,
  SebastopolGooseBreedOrganism: AnimalBreeds_SebastopolGooseBreedOrganism,
  SwedishIslandGooseBreedOrganism: AnimalBreeds_SwedishIslandGooseBreedOrganism,
  BronzeTurkeyBreedOrganism: AnimalBreeds_BronzeTurkeyBreedOrganism,
  AuburnTurkeyBreedOrganism: AnimalBreeds_AuburnTurkeyBreedOrganism,
  BourbonRedTurkeyBreedOrganism: AnimalBreeds_BourbonRedTurkeyBreedOrganism,
  BuffTurkeyBreedOrganism: AnimalBreeds_BuffTurkeyBreedOrganism,
  HuculHorseBreedOrganism: AnimalBreeds_HuculHorseBreedOrganism,
  AraAppaloosaHorseBreedOrganism: AnimalBreeds_AraAppaloosaHorseBreedOrganism,
  ArgentineCriolloHorseBreedOrganism: AnimalBreeds_ArgentineCriolloHorseBreedOrganism,
  ArgentinePoloPonyHorseBreedOrganism: AnimalBreeds_ArgentinePoloPonyHorseBreedOrganism,
  AustralianPonyHorseBreedOrganism: AnimalBreeds_AustralianPonyHorseBreedOrganism,
  AuxoisHorseBreedOrganism: AnimalBreeds_AuxoisHorseBreedOrganism,
  AveligneseHorseBreedOrganism: AnimalBreeds_AveligneseHorseBreedOrganism,
  AzerbaijanHorseBreedOrganism: AnimalBreeds_AzerbaijanHorseBreedOrganism,
  AzoresHorseBreedOrganism: AnimalBreeds_AzoresHorseBreedOrganism,
  BaliHorseBreedOrganism: AnimalBreeds_BaliHorseBreedOrganism,
  BalikunHorseBreedOrganism: AnimalBreeds_BalikunHorseBreedOrganism,
  WaziriHorseBreedOrganism: AnimalBreeds_WaziriHorseBreedOrganism,
  BankerHorseHorseBreedOrganism: AnimalBreeds_BankerHorseHorseBreedOrganism,
  BardigianoHorseBreedOrganism: AnimalBreeds_BardigianoHorseBreedOrganism,
  BatakHorseBreedOrganism: AnimalBreeds_BatakHorseBreedOrganism,
  BavarianWarmbloodHorseBreedOrganism: AnimalBreeds_BavarianWarmbloodHorseBreedOrganism,
  BelgianArdennaisHorseBreedOrganism: AnimalBreeds_BelgianArdennaisHorseBreedOrganism,
  BelgianHalfbloodHorseBreedOrganism: AnimalBreeds_BelgianHalfbloodHorseBreedOrganism,
  BelgianWarmbloodHorseBreedOrganism: AnimalBreeds_BelgianWarmbloodHorseBreedOrganism,
  BhutiaHorseBreedOrganism: AnimalBreeds_BhutiaHorseBreedOrganism,
  BlackSeaHorseHorseBreedOrganism: AnimalBreeds_BlackSeaHorseHorseBreedOrganism,
  BosnianHorseBreedOrganism: AnimalBreeds_BosnianHorseBreedOrganism,
  BoulonnaisHorseBreedOrganism: AnimalBreeds_BoulonnaisHorseBreedOrganism,
  BrandenburgHorseBreedOrganism: AnimalBreeds_BrandenburgHorseBreedOrganism,
  BrazilianSportHorseHorseBreedOrganism: AnimalBreeds_BrazilianSportHorseHorseBreedOrganism,
  BritishAppaloosaHorseBreedOrganism: AnimalBreeds_BritishAppaloosaHorseBreedOrganism,
  BritishRidingPonyHorseBreedOrganism: AnimalBreeds_BritishRidingPonyHorseBreedOrganism,
  BritishSpottedPonyHorseBreedOrganism: AnimalBreeds_BritishSpottedPonyHorseBreedOrganism,
  BuohaiHorseBreedOrganism: AnimalBreeds_BuohaiHorseBreedOrganism,
  BuryatHorseBreedOrganism: AnimalBreeds_BuryatHorseBreedOrganism,
  CalabrianHorseBreedOrganism: AnimalBreeds_CalabrianHorseBreedOrganism,
  CamargueHorseBreedOrganism: AnimalBreeds_CamargueHorseBreedOrganism,
  CanadianCuttingHorseHorseBreedOrganism: AnimalBreeds_CanadianCuttingHorseHorseBreedOrganism,
  CanadianRusticPonyHorseBreedOrganism: AnimalBreeds_CanadianRusticPonyHorseBreedOrganism,
  CanadianSportHorseHorseBreedOrganism: AnimalBreeds_CanadianSportHorseHorseBreedOrganism,
  CanikHorseBreedOrganism: AnimalBreeds_CanikHorseBreedOrganism,
  CapeHorseHorseBreedOrganism: AnimalBreeds_CapeHorseHorseBreedOrganism,
  CerbatHorseBreedOrganism: AnimalBreeds_CerbatHorseBreedOrganism,
  ChakouyiHorseBreedOrganism: AnimalBreeds_ChakouyiHorseBreedOrganism,
  CharaHorseHorseBreedOrganism: AnimalBreeds_CharaHorseHorseBreedOrganism,
  ChickasawHorseBreedOrganism: AnimalBreeds_ChickasawHorseBreedOrganism,
  ChiloteHorseBreedOrganism: AnimalBreeds_ChiloteHorseBreedOrganism,
  ChineseKazakhHorseBreedOrganism: AnimalBreeds_ChineseKazakhHorseBreedOrganism,
  ChineseMongolianHorseBreedOrganism: AnimalBreeds_ChineseMongolianHorseBreedOrganism,
  ChumbivilcasHorseBreedOrganism: AnimalBreeds_ChumbivilcasHorseBreedOrganism,
  ChumyshHorseBreedOrganism: AnimalBreeds_ChumyshHorseBreedOrganism,
  CiritHorseBreedOrganism: AnimalBreeds_CiritHorseBreedOrganism,
  IrishDraftHorseBreedOrganism: AnimalBreeds_IrishDraftHorseBreedOrganism,
  IrishHunterHorseBreedOrganism: AnimalBreeds_IrishHunterHorseBreedOrganism,
  CubanTrotterHorseBreedOrganism: AnimalBreeds_CubanTrotterHorseBreedOrganism,
  ItalianHeavyDraftHorseBreedOrganism: AnimalBreeds_ItalianHeavyDraftHorseBreedOrganism,
  JabeHorseBreedOrganism: AnimalBreeds_JabeHorseBreedOrganism,
  JavaHorseBreedOrganism: AnimalBreeds_JavaHorseBreedOrganism,
  VendenSheepBreedOrganism: AnimalBreeds_VendenSheepBreedOrganism,
  CzechWarmbloodHorseBreedOrganism: AnimalBreeds_CzechWarmbloodHorseBreedOrganism,
  JinhongHorseBreedOrganism: AnimalBreeds_JinhongHorseBreedOrganism,
  JinzhouHorseBreedOrganism: AnimalBreeds_JinzhouHorseBreedOrganism,
  SaxonyBreedDuckOrganism: AnimalBreeds_SaxonyBreedDuckOrganism,
  DanubianHorseBreedOrganism: AnimalBreeds_DanubianHorseBreedOrganism,
  KarachaiHorseBreedOrganism: AnimalBreeds_KarachaiHorseBreedOrganism,
  KarakacanHorseBreedOrganism: AnimalBreeds_KarakacanHorseBreedOrganism,
  KathiawariHorseBreedOrganism: AnimalBreeds_KathiawariHorseBreedOrganism,
  KeErQinHorseBreedOrganism: AnimalBreeds_KeErQinHorseBreedOrganism,
  KirgizHorseBreedOrganism: AnimalBreeds_KirgizHorseBreedOrganism,
  KuznetHorseBreedOrganism: AnimalBreeds_KuznetHorseBreedOrganism,
  LandaisHorseBreedOrganism: AnimalBreeds_LandaisHorseBreedOrganism,
  LewitzerHorseBreedOrganism: AnimalBreeds_LewitzerHorseBreedOrganism,
  LichuanHorseBreedOrganism: AnimalBreeds_LichuanHorseBreedOrganism,
  LijiangHorseBreedOrganism: AnimalBreeds_LijiangHorseBreedOrganism,
  LlaneroHorseBreedOrganism: AnimalBreeds_LlaneroHorseBreedOrganism,
  LombokHorseBreedOrganism: AnimalBreeds_LombokHorseBreedOrganism,
  LundyPonyHorseBreedOrganism: AnimalBreeds_LundyPonyHorseBreedOrganism,
  MalakanHorseBreedOrganism: AnimalBreeds_MalakanHorseBreedOrganism,
  MalopolskiHorseBreedOrganism: AnimalBreeds_MalopolskiHorseBreedOrganism,
  DatongHorseBreedOrganism: AnimalBreeds_DatongHorseBreedOrganism,
  MangalargaPaulistaHorseBreedOrganism: AnimalBreeds_MangalargaPaulistaHorseBreedOrganism,
  DulmenPonyHorseBreedOrganism: AnimalBreeds_DulmenPonyHorseBreedOrganism,
  MaremmanaHorseBreedOrganism: AnimalBreeds_MaremmanaHorseBreedOrganism,
  MarwariHorseBreedOrganism: AnimalBreeds_MarwariHorseBreedOrganism,
  MegezhHorseBreedOrganism: AnimalBreeds_MegezhHorseBreedOrganism,
  MegrelHorseBreedOrganism: AnimalBreeds_MegrelHorseBreedOrganism,
  MerensHorseBreedOrganism: AnimalBreeds_MerensHorseBreedOrganism,
  MessaraHorseBreedOrganism: AnimalBreeds_MessaraHorseBreedOrganism,
  SumbaHorseBreedOrganism: AnimalBreeds_SumbaHorseBreedOrganism,
  SumbawaHorseBreedOrganism: AnimalBreeds_SumbawaHorseBreedOrganism,
  SwedishArdennesHorseBreedOrganism: AnimalBreeds_SwedishArdennesHorseBreedOrganism,
  DutchTuigpaardHorseBreedOrganism: AnimalBreeds_DutchTuigpaardHorseBreedOrganism,
  EastAndSoutheastAnadoluHorseBreedOrganism: AnimalBreeds_EastAndSoutheastAnadoluHorseBreedOrganism,
  ThaiPonyHorseBreedOrganism: AnimalBreeds_ThaiPonyHorseBreedOrganism,
  ThessalonianHorseBreedOrganism: AnimalBreeds_ThessalonianHorseBreedOrganism,
  TibetanHorseBreedOrganism: AnimalBreeds_TibetanHorseBreedOrganism,
  TielingHorseBreedOrganism: AnimalBreeds_TielingHorseBreedOrganism,
  TimorHorseBreedOrganism: AnimalBreeds_TimorHorseBreedOrganism,
  TrakyaHorseBreedOrganism: AnimalBreeds_TrakyaHorseBreedOrganism,
  TroteEnGallopeHorseBreedOrganism: AnimalBreeds_TroteEnGallopeHorseBreedOrganism,
  TurkomanHorseBreedOrganism: AnimalBreeds_TurkomanHorseBreedOrganism,
  TushinHorseBreedOrganism: AnimalBreeds_TushinHorseBreedOrganism,
  TuvaHorseBreedOrganism: AnimalBreeds_TuvaHorseBreedOrganism,
  UzunyaylaHorseBreedOrganism: AnimalBreeds_UzunyaylaHorseBreedOrganism,
  VoronezhCoachHorseHorseBreedOrganism: AnimalBreeds_VoronezhCoachHorseHorseBreedOrganism,
  ElegantWarmbloodHorseBreedOrganism: AnimalBreeds_ElegantWarmbloodHorseBreedOrganism,
  WelshCobHorseBreedOrganism: AnimalBreeds_WelshCobHorseBreedOrganism,
  WelshMountainPonyHorseBreedOrganism: AnimalBreeds_WelshMountainPonyHorseBreedOrganism,
  EnglishHackHorseBreedOrganism: AnimalBreeds_EnglishHackHorseBreedOrganism,
  WurttembergHorseBreedOrganism: AnimalBreeds_WurttembergHorseBreedOrganism,
  XilingolHorseBreedOrganism: AnimalBreeds_XilingolHorseBreedOrganism,
  YanqiHorseBreedOrganism: AnimalBreeds_YanqiHorseBreedOrganism,
  YemeniHorsesHorseBreedOrganism: AnimalBreeds_YemeniHorsesHorseBreedOrganism,
  YiliHorseBreedOrganism: AnimalBreeds_YiliHorseBreedOrganism,
  YiwuHorseBreedOrganism: AnimalBreeds_YiwuHorseBreedOrganism,
  YunnanHorseBreedOrganism: AnimalBreeds_YunnanHorseBreedOrganism,
  GermanRidingPonyHorseBreedOrganism: AnimalBreeds_GermanRidingPonyHorseBreedOrganism,
  GuanzhongHorseBreedOrganism: AnimalBreeds_GuanzhongHorseBreedOrganism,
  GuizhouHorseBreedOrganism: AnimalBreeds_GuizhouHorseBreedOrganism,
  GuoxiaHorseBreedOrganism: AnimalBreeds_GuoxiaHorseBreedOrganism,
  ErlunchunHorseBreedOrganism: AnimalBreeds_ErlunchunHorseBreedOrganism,
  HalfSaddlebredHorseBreedOrganism: AnimalBreeds_HalfSaddlebredHorseBreedOrganism,
  FloresHorseBreedOrganism: AnimalBreeds_FloresHorseBreedOrganism,
  FreibergHorseBreedOrganism: AnimalBreeds_FreibergHorseBreedOrganism,
  HessenHorseBreedOrganism: AnimalBreeds_HessenHorseBreedOrganism,
  HinisHorseBreedOrganism: AnimalBreeds_HinisHorseBreedOrganism,
  HirzaiHorseBreedOrganism: AnimalBreeds_HirzaiHorseBreedOrganism,
  HungarianColdbloodHorseBreedOrganism: AnimalBreeds_HungarianColdbloodHorseBreedOrganism,
  HungarianDunHorseBreedOrganism: AnimalBreeds_HungarianDunHorseBreedOrganism,
  HungarianSportHorseHorseBreedOrganism: AnimalBreeds_HungarianSportHorseHorseBreedOrganism,
  InternationalStripedHorseHorseBreedOrganism: AnimalBreeds_InternationalStripedHorseHorseBreedOrganism,
  IrishCobHorseBreedOrganism: AnimalBreeds_IrishCobHorseBreedOrganism,
  MezenHorseBreedOrganism: AnimalBreeds_MezenHorseBreedOrganism,
  MezohegyesSportHorseHorseBreedOrganism: AnimalBreeds_MezohegyesSportHorseHorseBreedOrganism,
  FrenchCobHorseBreedOrganism: AnimalBreeds_FrenchCobHorseBreedOrganism,
  FrenchSaddlePonyHorseBreedOrganism: AnimalBreeds_FrenchSaddlePonyHorseBreedOrganism,
  MurakozHorseBreedOrganism: AnimalBreeds_MurakozHorseBreedOrganism,
  FinnhorseDraftHorseBreedOrganism: AnimalBreeds_FinnhorseDraftHorseBreedOrganism,
  MecklenburgHorseBreedOrganism: AnimalBreeds_MecklenburgHorseBreedOrganism,
  BaheijChickenBreedOrganism: AnimalBreeds_BaheijChickenBreedOrganism,
  AseelChickenBreedOrganism: AnimalBreeds_AseelChickenBreedOrganism,
  BandaraChickenBreedOrganism: AnimalBreeds_BandaraChickenBreedOrganism,
  CatalanaChickenBreedOrganism: AnimalBreeds_CatalanaChickenBreedOrganism,
  AustralianGamefowlChickenBreedOrganism: AnimalBreeds_AustralianGamefowlChickenBreedOrganism,
  AyamCemaniChickenBreedOrganism: AnimalBreeds_AyamCemaniChickenBreedOrganism,
  SwedishSpottedHenChickenBreedOrganism: AnimalBreeds_SwedishSpottedHenChickenBreedOrganism,
  BelgianBootedBantamChickenBreedOrganism: AnimalBreeds_BelgianBootedBantamChickenBreedOrganism,
  BrabanterChickenBreedOrganism: AnimalBreeds_BrabanterChickenBreedOrganism,
  BraekelChickenBreedOrganism: AnimalBreeds_BraekelChickenBreedOrganism,
  CochinStandardChickenBreedOrganism: AnimalBreeds_CochinStandardChickenBreedOrganism,
  OldEnglishBantamGameChickenBreedOrganism: AnimalBreeds_OldEnglishBantamGameChickenBreedOrganism,
  GotlandChickenBreedOrganism: AnimalBreeds_GotlandChickenBreedOrganism,
  GroningerMeeuwenChickenBreedOrganism: AnimalBreeds_GroningerMeeuwenChickenBreedOrganism,
  HaitiCrolePigBreedOrganism: AnimalBreeds_HaitiCrolePigBreedOrganism,
  ManorHybridPigBreedOrganism: AnimalBreeds_ManorHybridPigBreedOrganism,
  HamlinePigBreedOrganism: AnimalBreeds_HamlinePigBreedOrganism,
  ManorRangerPigBreedOrganism: AnimalBreeds_ManorRangerPigBreedOrganism,
  ManorMeishanPigBreedOrganism: AnimalBreeds_ManorMeishanPigBreedOrganism,
  CotswoldGoldPigBreedOrganism: AnimalBreeds_CotswoldGoldPigBreedOrganism,
  CotswoldPlatinumPigBreedOrganism: AnimalBreeds_CotswoldPlatinumPigBreedOrganism,
  Cotswold16PigBreedOrganism: AnimalBreeds_Cotswold16PigBreedOrganism,
  Cotswold29PigBreedOrganism: AnimalBreeds_Cotswold29PigBreedOrganism,
  Cotswold90PigBreedOrganism: AnimalBreeds_Cotswold90PigBreedOrganism,
  HampenPigBreedOrganism: AnimalBreeds_HampenPigBreedOrganism,
  SPMPigBreedOrganism: AnimalBreeds_SPMPigBreedOrganism,
  HighConformationWhitePigBreedOrganism: AnimalBreeds_HighConformationWhitePigBreedOrganism,
  Line32PigBreedOrganism: AnimalBreeds_Line32PigBreedOrganism,
  Line21PigBreedOrganism: AnimalBreeds_Line21PigBreedOrganism,
  MeatlinePigBreedOrganism: AnimalBreeds_MeatlinePigBreedOrganism,
  HamplinePigBreedOrganism: AnimalBreeds_HamplinePigBreedOrganism,
  EurolinePigBreedOrganism: AnimalBreeds_EurolinePigBreedOrganism,
  NorlinePigBreedOrganism: AnimalBreeds_NorlinePigBreedOrganism,
  PremierPigBreedOrganism: AnimalBreeds_PremierPigBreedOrganism,
  TribredPigBreedOrganism: AnimalBreeds_TribredPigBreedOrganism,
  AmericanEssexPigBreedOrganism: AnimalBreeds_AmericanEssexPigBreedOrganism,
  SinoGasconyPigBreedOrganism: AnimalBreeds_SinoGasconyPigBreedOrganism,
  GuadeloupeCrolePigBreedOrganism: AnimalBreeds_GuadeloupeCrolePigBreedOrganism,
  ManagraPigBreedOrganism: AnimalBreeds_ManagraPigBreedOrganism,
  CanadianLandracePigBreedOrganism: AnimalBreeds_CanadianLandracePigBreedOrganism,
  CanadianYorkshirePigBreedOrganism: AnimalBreeds_CanadianYorkshirePigBreedOrganism,
  PineywoodsPigBreedOrganism: AnimalBreeds_PineywoodsPigBreedOrganism,
  CatalinaIslandPigBreedOrganism: AnimalBreeds_CatalinaIslandPigBreedOrganism,
  RasNLansaPigBreedOrganism: AnimalBreeds_RasNLansaPigBreedOrganism,
  PitmanMooreMiniaturePigBreedOrganism: AnimalBreeds_PitmanMooreMiniaturePigBreedOrganism,
  VitaVetLabMinipigPigBreedOrganism: AnimalBreeds_VitaVetLabMinipigPigBreedOrganism,
  HanfordMiniaturePigBreedOrganism: AnimalBreeds_HanfordMiniaturePigBreedOrganism,
  BlackHampshirePigBreedOrganism: AnimalBreeds_BlackHampshirePigBreedOrganism,
  RedHampracePigBreedOrganism: AnimalBreeds_RedHampracePigBreedOrganism,
  AmericanYorkshirePigBreedOrganism: AnimalBreeds_AmericanYorkshirePigBreedOrganism,
  AmericanBerkshirePigBreedOrganism: AnimalBreeds_AmericanBerkshirePigBreedOrganism,
  CamboroughBluePigBreedOrganism: AnimalBreeds_CamboroughBluePigBreedOrganism,
  Camborough12PigBreedOrganism: AnimalBreeds_Camborough12PigBreedOrganism,
  WestrainPigBreedOrganism: AnimalBreeds_WestrainPigBreedOrganism,
  Dalland030PigBreedOrganism: AnimalBreeds_Dalland030PigBreedOrganism,
  RazorBackPigBreedOrganism: AnimalBreeds_RazorBackPigBreedOrganism,
  MacauPigBreedOrganism: AnimalBreeds_MacauPigBreedOrganism,
  MouraPigBreedOrganism: AnimalBreeds_MouraPigBreedOrganism,
  CanastraPigBreedOrganism: AnimalBreeds_CanastraPigBreedOrganism,
  PirapetingaPigBreedOrganism: AnimalBreeds_PirapetingaPigBreedOrganism,
  PiauPigBreedOrganism: AnimalBreeds_PiauPigBreedOrganism,
  NiloCanastraPigBreedOrganism: AnimalBreeds_NiloCanastraPigBreedOrganism,
  CanastroPigBreedOrganism: AnimalBreeds_CanastroPigBreedOrganism,
  CanastroJunqueiraPigBreedOrganism: AnimalBreeds_CanastroJunqueiraPigBreedOrganism,
  CanastroCapitoChicoPigBreedOrganism: AnimalBreeds_CanastroCapitoChicoPigBreedOrganism,
  CanastroZabumbaPigBreedOrganism: AnimalBreeds_CanastroZabumbaPigBreedOrganism,
  CanastroCabanoPigBreedOrganism: AnimalBreeds_CanastroCabanoPigBreedOrganism,
  CanastroVermelhoPigBreedOrganism: AnimalBreeds_CanastroVermelhoPigBreedOrganism,
  PiauCarunchoPiauPigBreedOrganism: AnimalBreeds_PiauCarunchoPiauPigBreedOrganism,
  CanastrinhoPigBreedOrganism: AnimalBreeds_CanastrinhoPigBreedOrganism,
  HondurasSwitchTailPigBreedOrganism: AnimalBreeds_HondurasSwitchTailPigBreedOrganism,
  MastergiltPigBreedOrganism: AnimalBreeds_MastergiltPigBreedOrganism,
  SovereignPigBreedOrganism: AnimalBreeds_SovereignPigBreedOrganism,
  PoltavaPigBreedOrganism: AnimalBreeds_PoltavaPigBreedOrganism,
  LipetskPigBreedOrganism: AnimalBreeds_LipetskPigBreedOrganism,
  SovietMeatPigBreedOrganism: AnimalBreeds_SovietMeatPigBreedOrganism,
  CentralRussianPigBreedOrganism: AnimalBreeds_CentralRussianPigBreedOrganism,
  SteppeMeatPigBreedOrganism: AnimalBreeds_SteppeMeatPigBreedOrganism,
  KharkovPigBreedOrganism: AnimalBreeds_KharkovPigBreedOrganism,
  DnepropetrovskPigBreedOrganism: AnimalBreeds_DnepropetrovskPigBreedOrganism,
  RussianLargeWhitePigBreedOrganism: AnimalBreeds_RussianLargeWhitePigBreedOrganism,
  ForestMountainPigBreedOrganism: AnimalBreeds_ForestMountainPigBreedOrganism,
  DnieperPigBreedOrganism: AnimalBreeds_DnieperPigBreedOrganism,
  IberianPigBreedOrganism: AnimalBreeds_IberianPigBreedOrganism,
  IberianExtremaduraRedPigBreedOrganism: AnimalBreeds_IberianExtremaduraRedPigBreedOrganism,
  IberianJabugoSpottedPigBreedOrganism: AnimalBreeds_IberianJabugoSpottedPigBreedOrganism,
  IberianBlackIberianPigBreedOrganism: AnimalBreeds_IberianBlackIberianPigBreedOrganism,
  PhilippineNativeIlocosPigBreedOrganism: AnimalBreeds_PhilippineNativeIlocosPigBreedOrganism,
  PhilippineNativeJalajalaPigBreedOrganism: AnimalBreeds_PhilippineNativeJalajalaPigBreedOrganism,
  MangaliztaPigBreedOrganism: AnimalBreeds_MangaliztaPigBreedOrganism,
  AlentejanaPigBreedOrganism: AnimalBreeds_AlentejanaPigBreedOrganism,
  BelgianLandraceBNPigBreedOrganism: AnimalBreeds_BelgianLandraceBNPigBreedOrganism,
  FrenchLargeWhitePigBreedOrganism: AnimalBreeds_FrenchLargeWhitePigBreedOrganism,
  HyperLargeWhitePigBreedOrganism: AnimalBreeds_HyperLargeWhitePigBreedOrganism,
  TiaMeslanPigBreedOrganism: AnimalBreeds_TiaMeslanPigBreedOrganism,
  PenArLan77PigBreedOrganism: AnimalBreeds_PenArLan77PigBreedOrganism,
  PenshirePigBreedOrganism: AnimalBreeds_PenshirePigBreedOrganism,
  LaconiePigBreedOrganism: AnimalBreeds_LaconiePigBreedOrganism,
  MurcianPigBreedOrganism: AnimalBreeds_MurcianPigBreedOrganism,
  CavallinoPigBreedOrganism: AnimalBreeds_CavallinoPigBreedOrganism,
  CalabrianPigBreedOrganism: AnimalBreeds_CalabrianPigBreedOrganism,
  ApulianPigBreedOrganism: AnimalBreeds_ApulianPigBreedOrganism,
  SienaBeltedPigBreedOrganism: AnimalBreeds_SienaBeltedPigBreedOrganism,
  CalascibettaPigBreedOrganism: AnimalBreeds_CalascibettaPigBreedOrganism,
  GssingForestPigPigBreedOrganism: AnimalBreeds_GssingForestPigPigBreedOrganism,
  SwissEdelschweinPigBreedOrganism: AnimalBreeds_SwissEdelschweinPigBreedOrganism,
  NorthCaucasusPigBreedOrganism: AnimalBreeds_NorthCaucasusPigBreedOrganism,
  DonPigBreedOrganism: AnimalBreeds_DonPigBreedOrganism,
  RostovPigBreedOrganism: AnimalBreeds_RostovPigBreedOrganism,
  RussianLongEaredWhitePigBreedOrganism: AnimalBreeds_RussianLongEaredWhitePigBreedOrganism,
  RussianShortEaredWhitePigBreedOrganism: AnimalBreeds_RussianShortEaredWhitePigBreedOrganism,
  PrisheksninskPigBreedOrganism: AnimalBreeds_PrisheksninskPigBreedOrganism,
  BreitovPigBreedOrganism: AnimalBreeds_BreitovPigBreedOrganism,
  LivnyPigBreedOrganism: AnimalBreeds_LivnyPigBreedOrganism,
  TsivilskPigBreedOrganism: AnimalBreeds_TsivilskPigBreedOrganism,
  UrzhumPigBreedOrganism: AnimalBreeds_UrzhumPigBreedOrganism,
  MinisibPigBreedOrganism: AnimalBreeds_MinisibPigBreedOrganism,
  SakhalinWhitePigBreedOrganism: AnimalBreeds_SakhalinWhitePigBreedOrganism,
  NorthSiberianPigBreedOrganism: AnimalBreeds_NorthSiberianPigBreedOrganism,
  SiberianBlackPiedPigBreedOrganism: AnimalBreeds_SiberianBlackPiedPigBreedOrganism,
  KemerovoPigBreedOrganism: AnimalBreeds_KemerovoPigBreedOrganism,
  KM1PigBreedOrganism: AnimalBreeds_KM1PigBreedOrganism,
  AksaBlackPiedPigBreedOrganism: AnimalBreeds_AksaBlackPiedPigBreedOrganism,
  SemirechenskPigBreedOrganism: AnimalBreeds_SemirechenskPigBreedOrganism,
  MinPigBreedOrganism: AnimalBreeds_MinPigBreedOrganism,
  SanjiangWhitePigBreedOrganism: AnimalBreeds_SanjiangWhitePigBreedOrganism,
  BasqueBlackPiedPigBreedOrganism: AnimalBreeds_BasqueBlackPiedPigBreedOrganism,
  CorsicanPigBreedOrganism: AnimalBreeds_CorsicanPigBreedOrganism,
  CrolePigBreedOrganism: AnimalBreeds_CrolePigBreedOrganism,
  GasconyPigBreedOrganism: AnimalBreeds_GasconyPigBreedOrganism,
  LimousinPigBreedOrganism: AnimalBreeds_LimousinPigBreedOrganism,
  HarbinWhitePigBreedOrganism: AnimalBreeds_HarbinWhitePigBreedOrganism,
  HeilongjiangSpottedPigBreedOrganism: AnimalBreeds_HeilongjiangSpottedPigBreedOrganism,
  LiaoningBlackPigBreedOrganism: AnimalBreeds_LiaoningBlackPigBreedOrganism,
  HuangHuaiHaiBlackShenxianPigBreedOrganism: AnimalBreeds_HuangHuaiHaiBlackShenxianPigBreedOrganism,
  HuangHuaiHaiBlackPigBreedOrganism: AnimalBreeds_HuangHuaiHaiBlackPigBreedOrganism,
  BameiPigBreedOrganism: AnimalBreeds_BameiPigBreedOrganism,
  HanjiangBlackPigBreedOrganism: AnimalBreeds_HanjiangBlackPigBreedOrganism,
  DingPigBreedOrganism: AnimalBreeds_DingPigBreedOrganism,
  HuaiPigBreedOrganism: AnimalBreeds_HuaiPigBreedOrganism,
  NewHuaiPigBreedOrganism: AnimalBreeds_NewHuaiPigBreedOrganism,
  MashenPigBreedOrganism: AnimalBreeds_MashenPigBreedOrganism,
  YimengBlackPigBreedOrganism: AnimalBreeds_YimengBlackPigBreedOrganism,
  HetaoLopEarPigBreedOrganism: AnimalBreeds_HetaoLopEarPigBreedOrganism,
  KoreanNativePigBreedOrganism: AnimalBreeds_KoreanNativePigBreedOrganism,
  KoreanImprovedPigBreedOrganism: AnimalBreeds_KoreanImprovedPigBreedOrganism,
  PenbukPigBreedOrganism: AnimalBreeds_PenbukPigBreedOrganism,
  BeijingBlackPigBreedOrganism: AnimalBreeds_BeijingBlackPigBreedOrganism,
  ChenghuaPigBreedOrganism: AnimalBreeds_ChenghuaPigBreedOrganism,
  TaoyuanPigBreedOrganism: AnimalBreeds_TaoyuanPigBreedOrganism,
  TaiwanSmallBlackPigBreedOrganism: AnimalBreeds_TaiwanSmallBlackPigBreedOrganism,
  TaiwanSmallRedPigBreedOrganism: AnimalBreeds_TaiwanSmallRedPigBreedOrganism,
  GuanlingPigBreedOrganism: AnimalBreeds_GuanlingPigBreedOrganism,
  HuchuanMountainPigBreedOrganism: AnimalBreeds_HuchuanMountainPigBreedOrganism,
  RongchangPigBreedOrganism: AnimalBreeds_RongchangPigBreedOrganism,
  WujinPigBreedOrganism: AnimalBreeds_WujinPigBreedOrganism,
  DahePigBreedOrganism: AnimalBreeds_DahePigBreedOrganism,
  YananPigBreedOrganism: AnimalBreeds_YananPigBreedOrganism,
  SouthYunnanShortEaredPigBreedOrganism: AnimalBreeds_SouthYunnanShortEaredPigBreedOrganism,
  HainanLingaoPigBreedOrganism: AnimalBreeds_HainanLingaoPigBreedOrganism,
  HainanTunchangPigBreedOrganism: AnimalBreeds_HainanTunchangPigBreedOrganism,
  HainanWenchangPigBreedOrganism: AnimalBreeds_HainanWenchangPigBreedOrganism,
  LiangGuangSmallSpottedPigBreedOrganism: AnimalBreeds_LiangGuangSmallSpottedPigBreedOrganism,
  GermanPasturePigBreedOrganism: AnimalBreeds_GermanPasturePigBreedOrganism,
  PiauSorocabaPigBreedOrganism: AnimalBreeds_PiauSorocabaPigBreedOrganism,
  NiloPigBreedOrganism: AnimalBreeds_NiloPigBreedOrganism,
  BahiaPigBreedOrganism: AnimalBreeds_BahiaPigBreedOrganism,
  PernaCurtaPigBreedOrganism: AnimalBreeds_PernaCurtaPigBreedOrganism,
  CarunchinhoPigBreedOrganism: AnimalBreeds_CarunchinhoPigBreedOrganism,
  MandiPigBreedOrganism: AnimalBreeds_MandiPigBreedOrganism,
  OrehlaDeColherPigBreedOrganism: AnimalBreeds_OrehlaDeColherPigBreedOrganism,
  VenezuelanBlackPigBreedOrganism: AnimalBreeds_VenezuelanBlackPigBreedOrganism,
  BolivianPigBreedOrganism: AnimalBreeds_BolivianPigBreedOrganism,
  PelnPigBreedOrganism: AnimalBreeds_PelnPigBreedOrganism,
  MexicanWattledPigBreedOrganism: AnimalBreeds_MexicanWattledPigBreedOrganism,
  Dalland080PigBreedOrganism: AnimalBreeds_Dalland080PigBreedOrganism,
  MonarchPigBreedOrganism: AnimalBreeds_MonarchPigBreedOrganism,
  BisaroPigBreedOrganism: AnimalBreeds_BisaroPigBreedOrganism,
  BlackHairlessPigBreedOrganism: AnimalBreeds_BlackHairlessPigBreedOrganism,
  BlackMangalitsaPigBreedOrganism: AnimalBreeds_BlackMangalitsaPigBreedOrganism,
  BorghigianaPigBreedOrganism: AnimalBreeds_BorghigianaPigBreedOrganism,
  ChianinaPigBreedOrganism: AnimalBreeds_ChianinaPigBreedOrganism,
  CosentinaPigBreedOrganism: AnimalBreeds_CosentinaPigBreedOrganism,
  CuinoPigBreedOrganism: AnimalBreeds_CuinoPigBreedOrganism,
  FriuliBlackPigBreedOrganism: AnimalBreeds_FriuliBlackPigBreedOrganism,
  FumatiPigBreedOrganism: AnimalBreeds_FumatiPigBreedOrganism,
  GalicianPigBreedOrganism: AnimalBreeds_GalicianPigBreedOrganism,
  GermanBerkshirePigBreedOrganism: AnimalBreeds_GermanBerkshirePigBreedOrganism,
  GhoriPigBreedOrganism: AnimalBreeds_GhoriPigBreedOrganism,
  JianliPigBreedOrganism: AnimalBreeds_JianliPigBreedOrganism,
  LucanianPigBreedOrganism: AnimalBreeds_LucanianPigBreedOrganism,
  MaremmanaPigBreedOrganism: AnimalBreeds_MaremmanaPigBreedOrganism,
  MiamiPigBreedOrganism: AnimalBreeds_MiamiPigBreedOrganism,
  MontmorillonPigBreedOrganism: AnimalBreeds_MontmorillonPigBreedOrganism,
  OldSwedishSpottedPigBreedOrganism: AnimalBreeds_OldSwedishSpottedPigBreedOrganism,
  OliventinaPigBreedOrganism: AnimalBreeds_OliventinaPigBreedOrganism,
  ParmensePigBreedOrganism: AnimalBreeds_ParmensePigBreedOrganism,
  RomagnolaPigBreedOrganism: AnimalBreeds_RomagnolaPigBreedOrganism,
  SiberianPigBreedOrganism: AnimalBreeds_SiberianPigBreedOrganism,
  SmallWhitePigBreedOrganism: AnimalBreeds_SmallWhitePigBreedOrganism,
  BaltaretPigBreedOrganism: AnimalBreeds_BaltaretPigBreedOrganism,
  TunchangPigBreedOrganism: AnimalBreeds_TunchangPigBreedOrganism,
  SterlingPigBreedOrganism: AnimalBreeds_SterlingPigBreedOrganism,
  VichPigBreedOrganism: AnimalBreeds_VichPigBreedOrganism,
  VietnamesePigBreedOrganism: AnimalBreeds_VietnamesePigBreedOrganism,
  VitoriaPigBreedOrganism: AnimalBreeds_VitoriaPigBreedOrganism,
  WaiChowPigBreedOrganism: AnimalBreeds_WaiChowPigBreedOrganism,
  YorkshireBlueAndWhitePigBreedOrganism: AnimalBreeds_YorkshireBlueAndWhitePigBreedOrganism,
  Dalland020PigBreedOrganism: AnimalBreeds_Dalland020PigBreedOrganism,
  WiltshirePigBreedOrganism: AnimalBreeds_WiltshirePigBreedOrganism,
  HamrocPigBreedOrganism: AnimalBreeds_HamrocPigBreedOrganism,
  DRUTMTerminalsPigBreedOrganism: AnimalBreeds_DRUTMTerminalsPigBreedOrganism,
  Camborough22PigBreedOrganism: AnimalBreeds_Camborough22PigBreedOrganism,
  Camborough15PigBreedOrganism: AnimalBreeds_Camborough15PigBreedOrganism,
  PR1050PigBreedOrganism: AnimalBreeds_PR1050PigBreedOrganism,
  PR1075PigBreedOrganism: AnimalBreeds_PR1075PigBreedOrganism,
  ChryakPICPigBreedOrganism: AnimalBreeds_ChryakPICPigBreedOrganism,
  CanadianRoyalBluePigBreedOrganism: AnimalBreeds_CanadianRoyalBluePigBreedOrganism,
  Line500DurocPigBreedOrganism: AnimalBreeds_Line500DurocPigBreedOrganism,
  Bodmin950PigBreedOrganism: AnimalBreeds_Bodmin950PigBreedOrganism,
  CanadianDurocPigBreedOrganism: AnimalBreeds_CanadianDurocPigBreedOrganism,
  CanadianHampshirePigBreedOrganism: AnimalBreeds_CanadianHampshirePigBreedOrganism,
  BaXuyenPigBreedOrganism: AnimalBreeds_BaXuyenPigBreedOrganism,
  ArapawaIslandPigBreedOrganism: AnimalBreeds_ArapawaIslandPigBreedOrganism,
  WuzhishanPigBreedOrganism: AnimalBreeds_WuzhishanPigBreedOrganism,
  PhilippineNativePigBreedOrganism: AnimalBreeds_PhilippineNativePigBreedOrganism,
  SinclairMiniaturePigBreedOrganism: AnimalBreeds_SinclairMiniaturePigBreedOrganism,
  SaddlebackPigBreedOrganism: AnimalBreeds_SaddlebackPigBreedOrganism,
  YucatanMiniaturePigBreedOrganism: AnimalBreeds_YucatanMiniaturePigBreedOrganism,
  BantuPigBreedOrganism: AnimalBreeds_BantuPigBreedOrganism,
  TibetanPigBreedOrganism: AnimalBreeds_TibetanPigBreedOrganism,
  TuropoljePigBreedOrganism: AnimalBreeds_TuropoljePigBreedOrganism,
  VietnamesePotBelliedPigPigBreedOrganism: AnimalBreeds_VietnamesePotBelliedPigPigBreedOrganism,
  AmericanLandracePigBreedOrganism: AnimalBreeds_AmericanLandracePigBreedOrganism,
  SwallowBeliedMangalitzaPigBreedOrganism: AnimalBreeds_SwallowBeliedMangalitzaPigBreedOrganism,
  FengjingPigBreedOrganism: AnimalBreeds_FengjingPigBreedOrganism,
  FinnishLandracePigBreedOrganism: AnimalBreeds_FinnishLandracePigBreedOrganism,
  GuineaHogPigBreedOrganism: AnimalBreeds_GuineaHogPigBreedOrganism,
  HezuoPigBreedOrganism: AnimalBreeds_HezuoPigBreedOrganism,
  OssabawIslandPigBreedOrganism: AnimalBreeds_OssabawIslandPigBreedOrganism,
  KelePigBreedOrganism: AnimalBreeds_KelePigBreedOrganism,
  KrskopoljePigBreedOrganism: AnimalBreeds_KrskopoljePigBreedOrganism,
  KunekunePigBreedOrganism: AnimalBreeds_KunekunePigBreedOrganism,
  LargeBlackWhitePigBreedOrganism: AnimalBreeds_LargeBlackWhitePigBreedOrganism,
  LithuanianNativePigBreedOrganism: AnimalBreeds_LithuanianNativePigBreedOrganism,
  MeishanPigBreedOrganism: AnimalBreeds_MeishanPigBreedOrganism,
  JinhuaPigBreedOrganism: AnimalBreeds_JinhuaPigBreedOrganism,
  NingxiangPigBreedOrganism: AnimalBreeds_NingxiangPigBreedOrganism,
  MoraRomagnolaPigBreedOrganism: AnimalBreeds_MoraRomagnolaPigBreedOrganism,
  MukotaPigBreedOrganism: AnimalBreeds_MukotaPigBreedOrganism,
  MinzhuPigBreedOrganism: AnimalBreeds_MinzhuPigBreedOrganism,
  NeijiangPigBreedOrganism: AnimalBreeds_NeijiangPigBreedOrganism,
  MulefootPigBreedOrganism: AnimalBreeds_MulefootPigBreedOrganism,
  NormandPigBreedOrganism: AnimalBreeds_NormandPigBreedOrganism,
  AngelnSaddlebackPigBreedOrganism: AnimalBreeds_AngelnSaddlebackPigBreedOrganism,
  GreekLocalPigBreedOrganism: AnimalBreeds_GreekLocalPigBreedOrganism,
  IcelandicPigBreedOrganism: AnimalBreeds_IcelandicPigBreedOrganism,
  CasertanaPigBreedOrganism: AnimalBreeds_CasertanaPigBreedOrganism,
  MadonieSicilianPigBreedOrganism: AnimalBreeds_MadonieSicilianPigBreedOrganism,
  SardinianPigBreedOrganism: AnimalBreeds_SardinianPigBreedOrganism,
  SicilianPigBreedOrganism: AnimalBreeds_SicilianPigBreedOrganism,
  ZlotnikiSpottedPigBreedOrganism: AnimalBreeds_ZlotnikiSpottedPigBreedOrganism,
  ZlotnikiWhitePigBreedOrganism: AnimalBreeds_ZlotnikiWhitePigBreedOrganism,
  SiskaPigBreedOrganism: AnimalBreeds_SiskaPigBreedOrganism,
  SumadijaPigBreedOrganism: AnimalBreeds_SumadijaPigBreedOrganism,
  FroxfieldPygmyPigBreedOrganism: AnimalBreeds_FroxfieldPygmyPigBreedOrganism,
  DanishLargeWhitePigBreedOrganism: AnimalBreeds_DanishLargeWhitePigBreedOrganism,
  DanishDurocPigBreedOrganism: AnimalBreeds_DanishDurocPigBreedOrganism,
  DanishHampshirePigBreedOrganism: AnimalBreeds_DanishHampshirePigBreedOrganism,
  PigghamPigBreedOrganism: AnimalBreeds_PigghamPigBreedOrganism,
  NewYorkRedPigBreedOrganism: AnimalBreeds_NewYorkRedPigBreedOrganism,
  FinnishYorkshirePigBreedOrganism: AnimalBreeds_FinnishYorkshirePigBreedOrganism,
  DutchYorkshirePigBreedOrganism: AnimalBreeds_DutchYorkshirePigBreedOrganism,
  PulawyPigBreedOrganism: AnimalBreeds_PulawyPigBreedOrganism,
  PomeranianPigBreedOrganism: AnimalBreeds_PomeranianPigBreedOrganism,
  PolishLandracePigBreedOrganism: AnimalBreeds_PolishLandracePigBreedOrganism,
  EstonianBaconPigBreedOrganism: AnimalBreeds_EstonianBaconPigBreedOrganism,
  LatvianWhitePigBreedOrganism: AnimalBreeds_LatvianWhitePigBreedOrganism,
  LithuanianWhitePigBreedOrganism: AnimalBreeds_LithuanianWhitePigBreedOrganism,
  BKB1PigBreedOrganism: AnimalBreeds_BKB1PigBreedOrganism,
  BelorusBlackPiedPigBreedOrganism: AnimalBreeds_BelorusBlackPiedPigBreedOrganism,
  MirgorodPigBreedOrganism: AnimalBreeds_MirgorodPigBreedOrganism,
  LiangGuangSmallSpottedLuchuanPigBreedOrganism: AnimalBreeds_LiangGuangSmallSpottedLuchuanPigBreedOrganism,
  FujianSmallPigBreedOrganism: AnimalBreeds_FujianSmallPigBreedOrganism,
  NorthFujianBlackAndWhitePigBreedOrganism: AnimalBreeds_NorthFujianBlackAndWhitePigBreedOrganism,
  FuanSpottedPigBreedOrganism: AnimalBreeds_FuanSpottedPigBreedOrganism,
  PutianPigBreedOrganism: AnimalBreeds_PutianPigBreedOrganism,
  FuzhouBlackPigBreedOrganism: AnimalBreeds_FuzhouBlackPigBreedOrganism,
  MinbeiSpottedPigBreedOrganism: AnimalBreeds_MinbeiSpottedPigBreedOrganism,
  LantangPigBreedOrganism: AnimalBreeds_LantangPigBreedOrganism,
  LiangGuangSmallSpottedGuangdongSmallEarPigBreedOrganism: AnimalBreeds_LiangGuangSmallSpottedGuangdongSmallEarPigBreedOrganism,
  LonglinPigBreedOrganism: AnimalBreeds_LonglinPigBreedOrganism,
  YuedongBlackPigBreedOrganism: AnimalBreeds_YuedongBlackPigBreedOrganism,
  XiangPigBreedOrganism: AnimalBreeds_XiangPigBreedOrganism,
  CantonesePigBreedOrganism: AnimalBreeds_CantonesePigBreedOrganism,
  JinhuaDongyangPigBreedOrganism: AnimalBreeds_JinhuaDongyangPigBreedOrganism,
  JinhuaYongkangPigBreedOrganism: AnimalBreeds_JinhuaYongkangPigBreedOrganism,
  DaweiziPigBreedOrganism: AnimalBreeds_DaweiziPigBreedOrganism,
  HuazhongTwoEndBlackPigBreedOrganism: AnimalBreeds_HuazhongTwoEndBlackPigBreedOrganism,
  HuazhongTwoEndBlackJianliPigBreedOrganism: AnimalBreeds_HuazhongTwoEndBlackJianliPigBreedOrganism,
  HuazhongTwoEndBlackTongchengPigBreedOrganism: AnimalBreeds_HuazhongTwoEndBlackTongchengPigBreedOrganism,
  HuazhongTwoEndBlackSatzelingPigBreedOrganism: AnimalBreeds_HuazhongTwoEndBlackSatzelingPigBreedOrganism,
  GanzhongnanSpottedPigBreedOrganism: AnimalBreeds_GanzhongnanSpottedPigBreedOrganism,
  HangPigBreedOrganism: AnimalBreeds_HangPigBreedOrganism,
  LepingPigBreedOrganism: AnimalBreeds_LepingPigBreedOrganism,
  LongyouBlackPigBreedOrganism: AnimalBreeds_LongyouBlackPigBreedOrganism,
  WuyiBlackPigBreedOrganism: AnimalBreeds_WuyiBlackPigBreedOrganism,
  LeeSungPigBreedOrganism: AnimalBreeds_LeeSungPigBreedOrganism,
  LanYuPigBreedOrganism: AnimalBreeds_LanYuPigBreedOrganism,
  VietnameseYorkshirePigBreedOrganism: AnimalBreeds_VietnameseYorkshirePigBreedOrganism,
  YujiangPigBreedOrganism: AnimalBreeds_YujiangPigBreedOrganism,
  WanzheSpottedPigBreedOrganism: AnimalBreeds_WanzheSpottedPigBreedOrganism,
  WanzheSpottedChunanSpottedPigBreedOrganism: AnimalBreeds_WanzheSpottedChunanSpottedPigBreedOrganism,
  WanzheSpottedWannanSpottedPigBreedOrganism: AnimalBreeds_WanzheSpottedWannanSpottedPigBreedOrganism,
  ShengxianSpottedPigBreedOrganism: AnimalBreeds_ShengxianSpottedPigBreedOrganism,
  QingpingPigBreedOrganism: AnimalBreeds_QingpingPigBreedOrganism,
  XiangxiBlackPigBreedOrganism: AnimalBreeds_XiangxiBlackPigBreedOrganism,
  BamaxiangPigBreedOrganism: AnimalBreeds_BamaxiangPigBreedOrganism,
  TaihuPigBreedOrganism: AnimalBreeds_TaihuPigBreedOrganism,
  ErhulianPigBreedOrganism: AnimalBreeds_ErhulianPigBreedOrganism,
  JiaxingBlackPigBreedOrganism: AnimalBreeds_JiaxingBlackPigBreedOrganism,
  MiPigBreedOrganism: AnimalBreeds_MiPigBreedOrganism,
  ShahutouPigBreedOrganism: AnimalBreeds_ShahutouPigBreedOrganism,
  JiaoxiPigBreedOrganism: AnimalBreeds_JiaoxiPigBreedOrganism,
  ShanghaiWhitePigBreedOrganism: AnimalBreeds_ShanghaiWhitePigBreedOrganism,
  HubeiWhitePigBreedOrganism: AnimalBreeds_HubeiWhitePigBreedOrganism,
  XinjinPigBreedOrganism: AnimalBreeds_XinjinPigBreedOrganism,
  XinjinJilinBlackPigBreedOrganism: AnimalBreeds_XinjinJilinBlackPigBreedOrganism,
  XinjinNingAnPigBreedOrganism: AnimalBreeds_XinjinNingAnPigBreedOrganism,
  PigBreedOrganism: AnimalBreeds_PigBreedOrganism,
  DBIPigBreedOrganism: AnimalBreeds_DBIPigBreedOrganism,
  XinjinXinjinPigBreedOrganism: AnimalBreeds_XinjinXinjinPigBreedOrganism,
  MeixinPigBreedOrganism: AnimalBreeds_MeixinPigBreedOrganism,
  NorthEastChinaSpottedPigBreedOrganism: AnimalBreeds_NorthEastChinaSpottedPigBreedOrganism,
  FannongSpottedPigBreedOrganism: AnimalBreeds_FannongSpottedPigBreedOrganism,
  LaoshanPigBreedOrganism: AnimalBreeds_LaoshanPigBreedOrganism,
  NanjingBlackPigBreedOrganism: AnimalBreeds_NanjingBlackPigBreedOrganism,
  ShanxiBlackPigBreedOrganism: AnimalBreeds_ShanxiBlackPigBreedOrganism,
  GanzhouWhitePigBreedOrganism: AnimalBreeds_GanzhouWhitePigBreedOrganism,
  GuangxiWhitePigBreedOrganism: AnimalBreeds_GuangxiWhitePigBreedOrganism,
  HanzhongWhitePigBreedOrganism: AnimalBreeds_HanzhongWhitePigBreedOrganism,
  LutaiWhitePigBreedOrganism: AnimalBreeds_LutaiWhitePigBreedOrganism,
  YiliWhitePigBreedOrganism: AnimalBreeds_YiliWhitePigBreedOrganism,
  XinjiangWhitePigBreedOrganism: AnimalBreeds_XinjiangWhitePigBreedOrganism,
  BSIPigBreedOrganism: AnimalBreeds_BSIPigBreedOrganism,
  MongCaiPigBreedOrganism: AnimalBreeds_MongCaiPigBreedOrganism,
  LangHongPigBreedOrganism: AnimalBreeds_LangHongPigBreedOrganism,
  MuongKhuongPigBreedOrganism: AnimalBreeds_MuongKhuongPigBreedOrganism,
  MeoPigBreedOrganism: AnimalBreeds_MeoPigBreedOrganism,
  TongConPigBreedOrganism: AnimalBreeds_TongConPigBreedOrganism,
  HaBacPigBreedOrganism: AnimalBreeds_HaBacPigBreedOrganism,
  ThaiBinhPigBreedOrganism: AnimalBreeds_ThaiBinhPigBreedOrganism,
  CoPigBreedOrganism: AnimalBreeds_CoPigBreedOrganism,
  SwissImprovedLandracePigBreedOrganism: AnimalBreeds_SwissImprovedLandracePigBreedOrganism,
  GermanLandraceBPigBreedOrganism: AnimalBreeds_GermanLandraceBPigBreedOrganism,
  EdelschweinPigBreedOrganism: AnimalBreeds_EdelschweinPigBreedOrganism,
  SwabianHallPigBreedOrganism: AnimalBreeds_SwabianHallPigBreedOrganism,
  BentheimBlackPiedPigBreedOrganism: AnimalBreeds_BentheimBlackPiedPigBreedOrganism,
  BaldingerSpottedPigBreedOrganism: AnimalBreeds_BaldingerSpottedPigBreedOrganism,
  GermanRedPiedPigBreedOrganism: AnimalBreeds_GermanRedPiedPigBreedOrganism,
  GermanCornwallPigBreedOrganism: AnimalBreeds_GermanCornwallPigBreedOrganism,
  GttingenMiniaturePigBreedOrganism: AnimalBreeds_GttingenMiniaturePigBreedOrganism,
  MunichMiniaturePigBreedOrganism: AnimalBreeds_MunichMiniaturePigBreedOrganism,
  LeicomaPigBreedOrganism: AnimalBreeds_LeicomaPigBreedOrganism,
  SchwerfurtMeatPigBreedOrganism: AnimalBreeds_SchwerfurtMeatPigBreedOrganism,
  HungarianWhitePigBreedOrganism: AnimalBreeds_HungarianWhitePigBreedOrganism,
  HungahybPigBreedOrganism: AnimalBreeds_HungahybPigBreedOrganism,
  BulgarianNativePigBreedOrganism: AnimalBreeds_BulgarianNativePigBreedOrganism,
  EastBalkanPigBreedOrganism: AnimalBreeds_EastBalkanPigBreedOrganism,
  KulaPigBreedOrganism: AnimalBreeds_KulaPigBreedOrganism,
  NghiaBinhPigBreedOrganism: AnimalBreeds_NghiaBinhPigBreedOrganism,
  BergamascaSheepBreedOrganism: AnimalBreeds_BergamascaSheepBreedOrganism,
  PortlandSheepBreedOrganism: AnimalBreeds_PortlandSheepBreedOrganism,
  WeisseHornloseHeidschnuckeSheepBreedOrganism: AnimalBreeds_WeisseHornloseHeidschnuckeSheepBreedOrganism,
  DrentsHeideschaapSheepBreedOrganism: AnimalBreeds_DrentsHeideschaapSheepBreedOrganism,
  KameroenSheepBreedOrganism: AnimalBreeds_KameroenSheepBreedOrganism,
  MergellandSheepBreedOrganism: AnimalBreeds_MergellandSheepBreedOrganism,
  OuessantSheepBreedOrganism: AnimalBreeds_OuessantSheepBreedOrganism,
  CanadianArcottSheepBreedOrganism: AnimalBreeds_CanadianArcottSheepBreedOrganism,
  NoordhollanderSheepBreedOrganism: AnimalBreeds_NoordhollanderSheepBreedOrganism,
  ChocolateTurkeyBreedOrganism: AnimalBreeds_ChocolateTurkeyBreedOrganism,
  LilacTurkeyBreedOrganism: AnimalBreeds_LilacTurkeyBreedOrganism,
  SilverAuburnTurkeyBreedOrganism: AnimalBreeds_SilverAuburnTurkeyBreedOrganism,
  WildCrossTurkeyOrganism: AnimalBreeds_WildCrossTurkeyOrganism,
  MatrouhChickenBreedOrganism: AnimalBreeds_MatrouhChickenBreedOrganism,
  RijnlamASheepBreedOrganism: AnimalBreeds_RijnlamASheepBreedOrganism,
  SchoonebekerSheepBreedOrganism: AnimalBreeds_SchoonebekerSheepBreedOrganism,
  WallisBlacknosedSheepBreedOrganism: AnimalBreeds_WallisBlacknosedSheepBreedOrganism,
  KhakiCampbellBreedDuckOrganism: AnimalBreeds_KhakiCampbellBreedDuckOrganism,
  NewfoundlandSheepBreedOrganism: AnimalBreeds_NewfoundlandSheepBreedOrganism,
  WallisCountrySheepBreedOrganism: AnimalBreeds_WallisCountrySheepBreedOrganism,
  RideauArcottSheepBreedOrganism: AnimalBreeds_RideauArcottSheepBreedOrganism,
  TukidaleSheepBreedOrganism: AnimalBreeds_TukidaleSheepBreedOrganism,
  PolwarthSheepBreedOrganism: AnimalBreeds_PolwarthSheepBreedOrganism,
  RyelandSheepBreedOrganism: AnimalBreeds_RyelandSheepBreedOrganism,
  ThalliSheepBreedOrganism: AnimalBreeds_ThalliSheepBreedOrganism,
  TongSheepBreedOrganism: AnimalBreeds_TongSheepBreedOrganism,
  TouabireSheepBreedOrganism: AnimalBreeds_TouabireSheepBreedOrganism,
  TunisSheepBreedOrganism: AnimalBreeds_TunisSheepBreedOrganism,
  TyrolMountainSheepBreedOrganism: AnimalBreeds_TyrolMountainSheepBreedOrganism,
  UdaSheepBreedOrganism: AnimalBreeds_UdaSheepBreedOrganism,
  GrayChickenBreedOrganism: AnimalBreeds_GrayChickenBreedOrganism,
  HamburgsChickenBreedOrganism: AnimalBreeds_HamburgsChickenBreedOrganism,
  GimmizahChickenBreedOrganism: AnimalBreeds_GimmizahChickenBreedOrganism,
  GermanMuttonMerinoSheepBreedOrganism: AnimalBreeds_GermanMuttonMerinoSheepBreedOrganism,
  MediumWoolMerinoSheepBreedOrganism: AnimalBreeds_MediumWoolMerinoSheepBreedOrganism,
  FonthillMerinoSheepBreedOrganism: AnimalBreeds_FonthillMerinoSheepBreedOrganism,
  SouthAfricanMuttonMerinoSheepBreedOrganism: AnimalBreeds_SouthAfricanMuttonMerinoSheepBreedOrganism,
  StrongWoolMerinoSheepBreedOrganism: AnimalBreeds_StrongWoolMerinoSheepBreedOrganism,
  PollMerinoSheepBreedOrganism: AnimalBreeds_PollMerinoSheepBreedOrganism,
  FineMerinoSheepBreedOrganism: AnimalBreeds_FineMerinoSheepBreedOrganism,
  SouthAfricanMerinoSheepBreedOrganism: AnimalBreeds_SouthAfricanMerinoSheepBreedOrganism,
  SuperfineMerinoSheepBreedOrganism: AnimalBreeds_SuperfineMerinoSheepBreedOrganism,
  DutchBantamChickenBreedOrganism: AnimalBreeds_DutchBantamChickenBreedOrganism,
  SilverMontazahChickenBreedOrganism: AnimalBreeds_SilverMontazahChickenBreedOrganism,
  SwissHenChickenBreedOrganism: AnimalBreeds_SwissHenChickenBreedOrganism,
  StyrianChickenBreedOrganism: AnimalBreeds_StyrianChickenBreedOrganism,
  OldEnglishGameChickenBreedOrganism: AnimalBreeds_OldEnglishGameChickenBreedOrganism,
  AppenzellBeardedHenChickenBreedOrganism: AnimalBreeds_AppenzellBeardedHenChickenBreedOrganism,
  BadenWurttemburgHorseBreedOrganism: AnimalBreeds_BadenWurttemburgHorseBreedOrganism,
  BritishWarmbloodHorseBreedOrganism: AnimalBreeds_BritishWarmbloodHorseBreedOrganism,
  IsraeliHorseBreedOrganism: AnimalBreeds_IsraeliHorseBreedOrganism,
  FrenchArdennaisHorseBreedOrganism: AnimalBreeds_FrenchArdennaisHorseBreedOrganism,
  BooroolaMerinoSheepBreedOrganism: AnimalBreeds_BooroolaMerinoSheepBreedOrganism,
  CukurovaHorseBreedOrganism: AnimalBreeds_CukurovaHorseBreedOrganism,
  CzechColdbloodHorseBreedOrganism: AnimalBreeds_CzechColdbloodHorseBreedOrganism,
  CzechoslovakianSmallRidingHorseHorseBreedOrganism: AnimalBreeds_CzechoslovakianSmallRidingHorseHorseBreedOrganism,
  JianchangHorseBreedOrganism: AnimalBreeds_JianchangHorseBreedOrganism,
  JielinHorseBreedOrganism: AnimalBreeds_JielinHorseBreedOrganism,
  WielkopolskiHorseBreedOrganism: AnimalBreeds_WielkopolskiHorseBreedOrganism,
  EleiaHorseBreedOrganism: AnimalBreeds_EleiaHorseBreedOrganism,
  EnglishCobHorseBreedOrganism: AnimalBreeds_EnglishCobHorseBreedOrganism,
  WelshPonyHorseBreedOrganism: AnimalBreeds_WelshPonyHorseBreedOrganism,
  WelshPonyOfCobTypeHorseBreedOrganism: AnimalBreeds_WelshPonyOfCobTypeHorseBreedOrganism,
  EnglishHunterHorseBreedOrganism: AnimalBreeds_EnglishHunterHorseBreedOrganism,
  EriskayPonyHorseBreedOrganism: AnimalBreeds_EriskayPonyHorseBreedOrganism,
  HackneyPonyHorseBreedOrganism: AnimalBreeds_HackneyPonyHorseBreedOrganism,
  EstonianDraftHorseBreedOrganism: AnimalBreeds_EstonianDraftHorseBreedOrganism,
  HeiheHorseBreedOrganism: AnimalBreeds_HeiheHorseBreedOrganism,
  HeilongkaingHorseBreedOrganism: AnimalBreeds_HeilongkaingHorseBreedOrganism,
  SulmtalerChickenBreedOrganism: AnimalBreeds_SulmtalerChickenBreedOrganism,
  SvartHnaChickenBreedOrganism: AnimalBreeds_SvartHnaChickenBreedOrganism,
  VorwerksChickenBreedOrganism: AnimalBreeds_VorwerksChickenBreedOrganism,
  WestfalischeTotlegerChickenBreedOrganism: AnimalBreeds_WestfalischeTotlegerChickenBreedOrganism,
  YamatoGunkeiChickenBreedOrganism: AnimalBreeds_YamatoGunkeiChickenBreedOrganism,
  DanishSportPonyHorseBreedOrganism: AnimalBreeds_DanishSportPonyHorseBreedOrganism,
  KabardaHorseBreedOrganism: AnimalBreeds_KabardaHorseBreedOrganism,
  KalmykHorseBreedOrganism: AnimalBreeds_KalmykHorseBreedOrganism,
  MangalargaMarchadorHorseBreedOrganism: AnimalBreeds_MangalargaMarchadorHorseBreedOrganism,
  DonHorseBreedOrganism: AnimalBreeds_DonHorseBreedOrganism,
  ManipuriHorseBreedOrganism: AnimalBreeds_ManipuriHorseBreedOrganism,
  SwissWarmbloodHorseBreedOrganism: AnimalBreeds_SwissWarmbloodHorseBreedOrganism,
  TavdaHorseBreedOrganism: AnimalBreeds_TavdaHorseBreedOrganism,
  EastBulgarianHorseBreedOrganism: AnimalBreeds_EastBulgarianHorseBreedOrganism,
  EastFriesianOldTypeHorseBreedOrganism: AnimalBreeds_EastFriesianOldTypeHorseBreedOrganism,
  EastFriesianWarmbloodModernTypeHorseBreedOrganism: AnimalBreeds_EastFriesianWarmbloodModernTypeHorseBreedOrganism,
  KakhetianPigBreedOrganism: AnimalBreeds_KakhetianPigBreedOrganism,
  WestFrenchWhitePigBreedOrganism: AnimalBreeds_WestFrenchWhitePigBreedOrganism,
  MiniatureHerefordCattleBreedOrganism: AnimalBreeds_MiniatureHerefordCattleBreedOrganism,
  JemJemZebuCattleBreedOrganism: AnimalBreeds_JemJemZebuCattleBreedOrganism,
  MinusinHorseBreedOrganism: AnimalBreeds_MinusinHorseBreedOrganism,
  MorochucoHorseBreedOrganism: AnimalBreeds_MorochucoHorseBreedOrganism,
  FrenchTrotterHorseBreedOrganism: AnimalBreeds_FrenchTrotterHorseBreedOrganism,
  FuriosoHorseBreedOrganism: AnimalBreeds_FuriosoHorseBreedOrganism,
  MurgheseHorseBreedOrganism: AnimalBreeds_MurgheseHorseBreedOrganism,
  MytileneHorseBreedOrganism: AnimalBreeds_MytileneHorseBreedOrganism,
  NamibDesertHorseHorseBreedOrganism: AnimalBreeds_NamibDesertHorseHorseBreedOrganism,
  DanishOldenborgHorseBreedOrganism: AnimalBreeds_DanishOldenborgHorseBreedOrganism,
  VolynskCattleBreedOrganism: AnimalBreeds_VolynskCattleBreedOrganism,
  SenepolCattleBreedOrganism: AnimalBreeds_SenepolCattleBreedOrganism,
  ShillukCattleBreedOrganism: AnimalBreeds_ShillukCattleBreedOrganism,
  SarPlaninaSheepBreedOrganism: AnimalBreeds_SarPlaninaSheepBreedOrganism,
  SantaInsSheepBreedOrganism: AnimalBreeds_SantaInsSheepBreedOrganism,
  SahelTypeSheepBreedOrganism: AnimalBreeds_SahelTypeSheepBreedOrganism,
  RygjaSheepBreedOrganism: AnimalBreeds_RygjaSheepBreedOrganism,
  RyaSheepBreedOrganism: AnimalBreeds_RyaSheepBreedOrganism,
  MoghaniSheepBreedOrganism: AnimalBreeds_MoghaniSheepBreedOrganism,
  RougeDeLQuoteQuestSheepBreedOrganism: AnimalBreeds_RougeDeLQuoteQuestSheepBreedOrganism,
  SoaySheepBreedOrganism: AnimalBreeds_SoaySheepBreedOrganism,
  SouthSuffolkSheepBreedOrganism: AnimalBreeds_SouthSuffolkSheepBreedOrganism,
  SouthWalesMountainSheepBreedOrganism: AnimalBreeds_SouthWalesMountainSheepBreedOrganism,
  SplsauSheepBreedOrganism: AnimalBreeds_SplsauSheepBreedOrganism,
  SpiegelSheepBreedOrganism: AnimalBreeds_SpiegelSheepBreedOrganism,
  StCroixSheepBreedOrganism: AnimalBreeds_StCroixSheepBreedOrganism,
  SteigarSheepBreedOrganism: AnimalBreeds_SteigarSheepBreedOrganism,
  SteinschafSheepBreedOrganism: AnimalBreeds_SteinschafSheepBreedOrganism,
  WelshMountainSheepBreedOrganism: AnimalBreeds_WelshMountainSheepBreedOrganism,
  SwedishFurSheepBreedOrganism: AnimalBreeds_SwedishFurSheepBreedOrganism,
  TeeswaterSheepBreedOrganism: AnimalBreeds_TeeswaterSheepBreedOrganism,
  TexelSheepBreedOrganism: AnimalBreeds_TexelSheepBreedOrganism,
  PelibeySheepBreedOrganism: AnimalBreeds_PelibeySheepBreedOrganism,
  MoradaNovaSheepBreedOrganism: AnimalBreeds_MoradaNovaSheepBreedOrganism,
  BalkhiSheepBreedOrganism: AnimalBreeds_BalkhiSheepBreedOrganism,
  BavarianForestSheepBreedOrganism: AnimalBreeds_BavarianForestSheepBreedOrganism,
  BarbadosBlackbellySheepBreedOrganism: AnimalBreeds_BarbadosBlackbellySheepBreedOrganism,
  RomneySheepBreedOrganism: AnimalBreeds_RomneySheepBreedOrganism,
  AwassiSheepBreedOrganism: AnimalBreeds_AwassiSheepBreedOrganism,
  ArapawaIslandSheepBreedOrganism: AnimalBreeds_ArapawaIslandSheepBreedOrganism,
  ArabiSheepBreedOrganism: AnimalBreeds_ArabiSheepBreedOrganism,
  ApennineSheepBreedOrganism: AnimalBreeds_ApennineSheepBreedOrganism,
  AmericanTunisSheepBreedOrganism: AnimalBreeds_AmericanTunisSheepBreedOrganism,
  BalwenWelshMountainSheepBreedOrganism: AnimalBreeds_BalwenWelshMountainSheepBreedOrganism,
  PrianganSheepBreedOrganism: AnimalBreeds_PrianganSheepBreedOrganism,
  RaboLargoSheepBreedOrganism: AnimalBreeds_RaboLargoSheepBreedOrganism,
  FrieslandsChickenBreedOrganism: AnimalBreeds_FrieslandsChickenBreedOrganism,
  AmeraucanaChickenBreedOrganism: AnimalBreeds_AmeraucanaChickenBreedOrganism,
  SbosChickenBreedOrganism: AnimalBreeds_SbosChickenBreedOrganism,
  AugsburgerChickenBreedOrganism: AnimalBreeds_AugsburgerChickenBreedOrganism,
  BielefelderChickenBreedOrganism: AnimalBreeds_BielefelderChickenBreedOrganism,
  NorfolkGrayChickenBreedOrganism: AnimalBreeds_NorfolkGrayChickenBreedOrganism,
  HedemoraChickenBreedOrganism: AnimalBreeds_HedemoraChickenBreedOrganism,
  HungarianYellowChickenBreedOrganism: AnimalBreeds_HungarianYellowChickenBreedOrganism,
  IowaBlueChickenBreedOrganism: AnimalBreeds_IowaBlueChickenBreedOrganism,
  RedJungleFowlTypeChickenOrganism: AnimalBreeds_RedJungleFowlTypeChickenOrganism,
  KoShamoChickenBreedOrganism: AnimalBreeds_KoShamoChickenBreedOrganism,
  KraienkoppeChickenBreedOrganism: AnimalBreeds_KraienkoppeChickenBreedOrganism,
  LincolnshireBuffChickenBreedOrganism: AnimalBreeds_LincolnshireBuffChickenBreedOrganism,
  TomaruChickenBreedOrganism: AnimalBreeds_TomaruChickenBreedOrganism,
  OnagadoriChickenBreedOrganism: AnimalBreeds_OnagadoriChickenBreedOrganism,
  ManxRumpiesChickenBreedOrganism: AnimalBreeds_ManxRumpiesChickenBreedOrganism,
  MalineChickenBreedOrganism: AnimalBreeds_MalineChickenBreedOrganism,
  NankinChickenBreedOrganism: AnimalBreeds_NankinChickenBreedOrganism,
  LandChickenBreedOrganism: AnimalBreeds_LandChickenBreedOrganism,
  LanskBantamChickenBreedOrganism: AnimalBreeds_LanskBantamChickenBreedOrganism,
  OrustChickenBreedOrganism: AnimalBreeds_OrustChickenBreedOrganism,
  PenedesencaChickenBreedOrganism: AnimalBreeds_PenedesencaChickenBreedOrganism,
  RhodeIslandWhiteChickenBreedOrganism: AnimalBreeds_RhodeIslandWhiteChickenBreedOrganism,
  RosecombBantamChickenBreedOrganism: AnimalBreeds_RosecombBantamChickenBreedOrganism,
  SablepootChickenBreedOrganism: AnimalBreeds_SablepootChickenBreedOrganism,
  SaipanJungleFowlChickenBreedOrganism: AnimalBreeds_SaipanJungleFowlChickenBreedOrganism,
  ScotsDumpyChickenBreedOrganism: AnimalBreeds_ScotsDumpyChickenBreedOrganism,
  MubanPigBreedOrganism: AnimalBreeds_MubanPigBreedOrganism,
  IbanPigBreedOrganism: AnimalBreeds_IbanPigBreedOrganism,
  AltaySheepBreedOrganism: AnimalBreeds_AltaySheepBreedOrganism,
  FaeroesSheepBreedOrganism: AnimalBreeds_FaeroesSheepBreedOrganism,
  GreenChickenBreedOrganism: AnimalBreeds_GreenChickenBreedOrganism,
  GallusInaurisChickenBreedOrganism: AnimalBreeds_GallusInaurisChickenBreedOrganism,
  PittIslandSheepBreedOrganism: AnimalBreeds_PittIslandSheepBreedOrganism,
  GoldenMontazahChickenBreedOrganism: AnimalBreeds_GoldenMontazahChickenBreedOrganism,
  PinziritaSheepBreedOrganism: AnimalBreeds_PinziritaSheepBreedOrganism,
  SardinianSheepBreedOrganism: AnimalBreeds_SardinianSheepBreedOrganism,
  EastFriesianSheepBreedOrganism: AnimalBreeds_EastFriesianSheepBreedOrganism,
  UjumqinSheepBreedOrganism: AnimalBreeds_UjumqinSheepBreedOrganism,
  DLSSheepBreedOrganism: AnimalBreeds_DLSSheepBreedOrganism,
  WalachenschafSheepBreedOrganism: AnimalBreeds_WalachenschafSheepBreedOrganism,
  OutaouaisArcottSheepBreedOrganism: AnimalBreeds_OutaouaisArcottSheepBreedOrganism,
  OssimiSheepBreedOrganism: AnimalBreeds_OssimiSheepBreedOrganism,
  BentheimerLandschafSheepBreedOrganism: AnimalBreeds_BentheimerLandschafSheepBreedOrganism,
  BarbadoSheepBreedOrganism: AnimalBreeds_BarbadoSheepBreedOrganism,
  BaluchiSheepBreedOrganism: AnimalBreeds_BaluchiSheepBreedOrganism,
  ShamoChickenBreedOrganism: AnimalBreeds_ShamoChickenBreedOrganism,
  AmericanIndianHorseHorseBreedOrganism: AnimalBreeds_AmericanIndianHorseHorseBreedOrganism,
  AmericanMustangHorseBreedOrganism: AnimalBreeds_AmericanMustangHorseBreedOrganism,
  AmericanQuarterHorseHorseBreedOrganism: AnimalBreeds_AmericanQuarterHorseHorseBreedOrganism,
  AmericanShetlandPonyHorseBreedOrganism: AnimalBreeds_AmericanShetlandPonyHorseBreedOrganism,
  AnadoluHorseBreedOrganism: AnimalBreeds_AnadoluHorseBreedOrganism,
  AndeanHorseBreedOrganism: AnimalBreeds_AndeanHorseBreedOrganism,
  AngloKabardaHorseBreedOrganism: AnimalBreeds_AngloKabardaHorseBreedOrganism,
  PoitouDonkeyBreedOrganism: AnimalBreeds_PoitouDonkeyBreedOrganism,
  MammothJackStockDonkeyBreedOrganism: AnimalBreeds_MammothJackStockDonkeyBreedOrganism,
  NarymHorseBreedOrganism: AnimalBreeds_NarymHorseBreedOrganism,
  NationalSpottedSaddleHorseHorseBreedOrganism: AnimalBreeds_NationalSpottedSaddleHorseHorseBreedOrganism,
  NigerianHorseBreedOrganism: AnimalBreeds_NigerianHorseBreedOrganism,
  NorthSwedishTrotterHorseBreedOrganism: AnimalBreeds_NorthSwedishTrotterHorseBreedOrganism,
  OrientalHorseHorseBreedOrganism: AnimalBreeds_OrientalHorseHorseBreedOrganism,
  RhinelandHeavyDraftHorseBreedOrganism: AnimalBreeds_RhinelandHeavyDraftHorseBreedOrganism,
  RomanianSaddleHorseHorseBreedOrganism: AnimalBreeds_RomanianSaddleHorseHorseBreedOrganism,
  RottalHorseBreedOrganism: AnimalBreeds_RottalHorseBreedOrganism,
  RoyalCanadianMountedPoliceHorseHorseBreedOrganism: AnimalBreeds_RoyalCanadianMountedPoliceHorseHorseBreedOrganism,
  RussianSaddleHorseHorseBreedOrganism: AnimalBreeds_RussianSaddleHorseHorseBreedOrganism,
  SableIslandHorseHorseBreedOrganism: AnimalBreeds_SableIslandHorseHorseBreedOrganism,
  PanjeHorseBreedOrganism: AnimalBreeds_PanjeHorseBreedOrganism,
  PatibarcinaHorseBreedOrganism: AnimalBreeds_PatibarcinaHorseBreedOrganism,
  PechoraHorseBreedOrganism: AnimalBreeds_PechoraHorseBreedOrganism,
  PeneiaHorseBreedOrganism: AnimalBreeds_PeneiaHorseBreedOrganism,
  PerianganHorseBreedOrganism: AnimalBreeds_PerianganHorseBreedOrganism,
  PersianArabHorseBreedOrganism: AnimalBreeds_PersianArabHorseBreedOrganism,
  PetisoArgentinoHorseBreedOrganism: AnimalBreeds_PetisoArgentinoHorseBreedOrganism,
  PolishDraftHorseBreedOrganism: AnimalBreeds_PolishDraftHorseBreedOrganism,
  PriobHorseBreedOrganism: AnimalBreeds_PriobHorseBreedOrganism,
  RahvanHorseBreedOrganism: AnimalBreeds_RahvanHorseBreedOrganism,
  SalernoHorseBreedOrganism: AnimalBreeds_SalernoHorseBreedOrganism,
  SandalwoodHorseBreedOrganism: AnimalBreeds_SandalwoodHorseBreedOrganism,
  SandanHorseBreedOrganism: AnimalBreeds_SandanHorseBreedOrganism,
  PindosHorseBreedOrganism: AnimalBreeds_PindosHorseBreedOrganism,
  PiquiraPonyHorseBreedOrganism: AnimalBreeds_PiquiraPonyHorseBreedOrganism,
  PlevenHorseBreedOrganism: AnimalBreeds_PlevenHorseBreedOrganism,
  LargeStandardDonkeyOrganism: AnimalBreeds_LargeStandardDonkeyOrganism,
  StandardDonkeyOrganism: AnimalBreeds_StandardDonkeyOrganism,
  MiniatureDonkeyOrganism: AnimalBreeds_MiniatureDonkeyOrganism,
  GarranoTarpanHorseXDomesticHorseBreedOrganism: AnimalBreeds_GarranoTarpanHorseXDomesticHorseBreedOrganism,
  KoninkTarpanHorseXDomesticHorseBreedOrganism: AnimalBreeds_KoninkTarpanHorseXDomesticHorseBreedOrganism,
  AsturianTarpanHorseXDomesticHorseBreedOrganism: AnimalBreeds_AsturianTarpanHorseXDomesticHorseBreedOrganism,
  PottokTarpanHorseXDomesticHorseBreedOrganism: AnimalBreeds_PottokTarpanHorseXDomesticHorseBreedOrganism,
  RussianTrotterHorseBreedOrganism: AnimalBreeds_RussianTrotterHorseBreedOrganism,
  WestAfricanBarbHorseBreedOrganism: AnimalBreeds_WestAfricanBarbHorseBreedOrganism,
  FellPonyHorseBreedOrganism: AnimalBreeds_FellPonyHorseBreedOrganism,
  NationalShowHorseHorseBreedOrganism: AnimalBreeds_NationalShowHorseHorseBreedOrganism,
  ZhemaichuHorseBreedOrganism: AnimalBreeds_ZhemaichuHorseBreedOrganism,
  YonaguniHorseBreedOrganism: AnimalBreeds_YonaguniHorseBreedOrganism,
  YakutHorseBreedOrganism: AnimalBreeds_YakutHorseBreedOrganism,
  TawleedHorseBreedOrganism: AnimalBreeds_TawleedHorseBreedOrganism,
  WesternSudanPonyHorseBreedOrganism: AnimalBreeds_WesternSudanPonyHorseBreedOrganism,
  WeleraPonyHorseBreedOrganism: AnimalBreeds_WeleraPonyHorseBreedOrganism,
  VyatkaHorseBreedOrganism: AnimalBreeds_VyatkaHorseBreedOrganism,
  VladimirHeavyDraftHorseBreedOrganism: AnimalBreeds_VladimirHeavyDraftHorseBreedOrganism,
  VlaamperdHorseBreedOrganism: AnimalBreeds_VlaamperdHorseBreedOrganism,
  UkrainianSaddleHorseHorseBreedOrganism: AnimalBreeds_UkrainianSaddleHorseHorseBreedOrganism,
  ToriHorseBreedOrganism: AnimalBreeds_ToriHorseBreedOrganism,
  TokaraHorseBreedOrganism: AnimalBreeds_TokaraHorseBreedOrganism,
  NewKirgizHorseBreedOrganism: AnimalBreeds_NewKirgizHorseBreedOrganism,
  OldenburgHorseBreedOrganism: AnimalBreeds_OldenburgHorseBreedOrganism,
  MisakiHorseBreedOrganism: AnimalBreeds_MisakiHorseBreedOrganism,
  MiyakoHorseBreedOrganism: AnimalBreeds_MiyakoHorseBreedOrganism,
  MongolianHorseBreedOrganism: AnimalBreeds_MongolianHorseBreedOrganism,
  WalerHorseBreedOrganism: AnimalBreeds_WalerHorseBreedOrganism,
  DutchDraftHorseBreedOrganism: AnimalBreeds_DutchDraftHorseBreedOrganism,
  EgyptianHorseBreedOrganism: AnimalBreeds_EgyptianHorseBreedOrganism,
  EstonianNativeHorseBreedOrganism: AnimalBreeds_EstonianNativeHorseBreedOrganism,
  ExmoorPonyHorseBreedOrganism: AnimalBreeds_ExmoorPonyHorseBreedOrganism,
  FaeroesIslandHorseHorseBreedOrganism: AnimalBreeds_FaeroesIslandHorseHorseBreedOrganism,
  FalabellaHorseBreedOrganism: AnimalBreeds_FalabellaHorseBreedOrganism,
  DutchWarmbloodHorseBreedOrganism: AnimalBreeds_DutchWarmbloodHorseBreedOrganism,
  DongolaHorseBreedOrganism: AnimalBreeds_DongolaHorseBreedOrganism,
  DleHorseBreedOrganism: AnimalBreeds_DleHorseBreedOrganism,
  DjermaHorseBreedOrganism: AnimalBreeds_DjermaHorseBreedOrganism,
  DelibozHorseBreedOrganism: AnimalBreeds_DelibozHorseBreedOrganism,
  DartmoorPonyHorseBreedOrganism: AnimalBreeds_DartmoorPonyHorseBreedOrganism,
  CriouloHorseBreedOrganism: AnimalBreeds_CriouloHorseBreedOrganism,
  FinnhorseHorseBreedOrganism: AnimalBreeds_FinnhorseHorseBreedOrganism,
  SanfratelloHorseBreedOrganism: AnimalBreeds_SanfratelloHorseBreedOrganism,
  MorabHorseBreedOrganism: AnimalBreeds_MorabHorseBreedOrganism,
  MoyleHorseBreedOrganism: AnimalBreeds_MoyleHorseBreedOrganism,
  MustangHorseBreedOrganism: AnimalBreeds_MustangHorseBreedOrganism,
  MQuoteBayarHorseBreedOrganism: AnimalBreeds_MQuoteBayarHorseBreedOrganism,
  LusitanoHorseBreedOrganism: AnimalBreeds_LusitanoHorseBreedOrganism,
  NewfoundlandPonyHorseBreedOrganism: AnimalBreeds_NewfoundlandPonyHorseBreedOrganism,
  NomaHorseBreedOrganism: AnimalBreeds_NomaHorseBreedOrganism,
  NooitgedachtPonyHorseBreedOrganism: AnimalBreeds_NooitgedachtPonyHorseBreedOrganism,
  NordlandHorseBreedOrganism: AnimalBreeds_NordlandHorseBreedOrganism,
  NoricHorseBreedOrganism: AnimalBreeds_NoricHorseBreedOrganism,
  NorthSwedishHorseHorseBreedOrganism: AnimalBreeds_NorthSwedishHorseHorseBreedOrganism,
  NortheasternHorseBreedOrganism: AnimalBreeds_NortheasternHorseBreedOrganism,
  KisberFelverHorseBreedOrganism: AnimalBreeds_KisberFelverHorseBreedOrganism,
  AngloArabHorseBreedOrganism: AnimalBreeds_AngloArabHorseBreedOrganism,
  NoniusHorseBreedOrganism: AnimalBreeds_NoniusHorseBreedOrganism,
  NooitgedachtHorseBreedOrganism: AnimalBreeds_NooitgedachtHorseBreedOrganism,
  IomudHorseBreedOrganism: AnimalBreeds_IomudHorseBreedOrganism,
  JutlandHorseBreedOrganism: AnimalBreeds_JutlandHorseBreedOrganism,
  KarabairHorseBreedOrganism: AnimalBreeds_KarabairHorseBreedOrganism,
  KarabakhHorseBreedOrganism: AnimalBreeds_KarabakhHorseBreedOrganism,
  KazakhHorseBreedOrganism: AnimalBreeds_KazakhHorseBreedOrganism,
  MangalargaHorseBreedOrganism: AnimalBreeds_MangalargaHorseBreedOrganism,
  KirdiPonyHorseBreedOrganism: AnimalBreeds_KirdiPonyHorseBreedOrganism,
  KisoHorseBreedOrganism: AnimalBreeds_KisoHorseBreedOrganism,
  KladrubyHorseBreedOrganism: AnimalBreeds_KladrubyHorseBreedOrganism,
  KnabstrupHorseBreedOrganism: AnimalBreeds_KnabstrupHorseBreedOrganism,
  KushumHorseBreedOrganism: AnimalBreeds_KushumHorseBreedOrganism,
  KustanaiHorseBreedOrganism: AnimalBreeds_KustanaiHorseBreedOrganism,
  LatvianHorseBreedOrganism: AnimalBreeds_LatvianHorseBreedOrganism,
  LithuanianHeavyDraftHorseBreedOrganism: AnimalBreeds_LithuanianHeavyDraftHorseBreedOrganism,
  LokaiHorseBreedOrganism: AnimalBreeds_LokaiHorseBreedOrganism,
  KigerMustangHorseBreedOrganism: AnimalBreeds_KigerMustangHorseBreedOrganism,
  PonyOfTheAmericasHorseBreedOrganism: AnimalBreeds_PonyOfTheAmericasHorseBreedOrganism,
  PintabianHorseBreedOrganism: AnimalBreeds_PintabianHorseBreedOrganism,
  PantaneiroHorseBreedOrganism: AnimalBreeds_PantaneiroHorseBreedOrganism,
  OrlovTrotterHorseBreedOrganism: AnimalBreeds_OrlovTrotterHorseBreedOrganism,
  NorthernArdennaisHorseBreedOrganism: AnimalBreeds_NorthernArdennaisHorseBreedOrganism,
  AbtenauerHorseBreedOrganism: AnimalBreeds_AbtenauerHorseBreedOrganism,
  AdaevHorseBreedOrganism: AnimalBreeds_AdaevHorseBreedOrganism,
  AlbanianHorseBreedOrganism: AnimalBreeds_AlbanianHorseBreedOrganism,
  AlterRealHorseBreedOrganism: AnimalBreeds_AlterRealHorseBreedOrganism,
  AmericanBashkirCurlyHorseBreedOrganism: AnimalBreeds_AmericanBashkirCurlyHorseBreedOrganism,
  PoitouMuleProducerHorseBreedOrganism: AnimalBreeds_PoitouMuleProducerHorseBreedOrganism,
  PolesianHorseBreedOrganism: AnimalBreeds_PolesianHorseBreedOrganism,
  SardinianAngloArabHorseBreedOrganism: AnimalBreeds_SardinianAngloArabHorseBreedOrganism,
  SardinianPonyHorseBreedOrganism: AnimalBreeds_SardinianPonyHorseBreedOrganism,
  SarvarHorseBreedOrganism: AnimalBreeds_SarvarHorseBreedOrganism,
  SchleswigHorseBreedOrganism: AnimalBreeds_SchleswigHorseBreedOrganism,
  SchwarzwalderFuchseHorseBreedOrganism: AnimalBreeds_SchwarzwalderFuchseHorseBreedOrganism,
  SenneHorseBreedOrganism: AnimalBreeds_SenneHorseBreedOrganism,
  ShanHorseBreedOrganism: AnimalBreeds_ShanHorseBreedOrganism,
  SilesianHorseBreedOrganism: AnimalBreeds_SilesianHorseBreedOrganism,
  SiniHorseBreedOrganism: AnimalBreeds_SiniHorseBreedOrganism,
  SkyrosHorseBreedOrganism: AnimalBreeds_SkyrosHorseBreedOrganism,
  SlovakWarmbloodHorseBreedOrganism: AnimalBreeds_SlovakWarmbloodHorseBreedOrganism,
  SokolkaHorseBreedOrganism: AnimalBreeds_SokolkaHorseBreedOrganism,
  SouthAfricanMiniatureHorseBreedOrganism: AnimalBreeds_SouthAfricanMiniatureHorseBreedOrganism,
  SouthGermanColdbloodHorseBreedOrganism: AnimalBreeds_SouthGermanColdbloodHorseBreedOrganism,
  SouthwestSpanishMustangHorseBreedOrganism: AnimalBreeds_SouthwestSpanishMustangHorseBreedOrganism,
  SpanishAmericanHorseHorseBreedOrganism: AnimalBreeds_SpanishAmericanHorseHorseBreedOrganism,
  SpanishAngloArabHorseBreedOrganism: AnimalBreeds_SpanishAngloArabHorseBreedOrganism,
  SpanishColonialHorseHorseBreedOrganism: AnimalBreeds_SpanishColonialHorseHorseBreedOrganism,
  SpitiHorseBreedOrganism: AnimalBreeds_SpitiHorseBreedOrganism,
  SulawesiHorseBreedOrganism: AnimalBreeds_SulawesiHorseBreedOrganism,
  CriolloHorseBreedOrganism: AnimalBreeds_CriolloHorseBreedOrganism,
  HequHorseBreedOrganism: AnimalBreeds_HequHorseBreedOrganism,
  ConnemaraPonyHorseBreedOrganism: AnimalBreeds_ConnemaraPonyHorseBreedOrganism,
  ColoradoRangerHorseBreedOrganism: AnimalBreeds_ColoradoRangerHorseBreedOrganism,
  DalesPonyHorseBreedOrganism: AnimalBreeds_DalesPonyHorseBreedOrganism,
  GotlandHorseBreedOrganism: AnimalBreeds_GotlandHorseBreedOrganism,
  ChincoteaguePonyHorseBreedOrganism: AnimalBreeds_ChincoteaguePonyHorseBreedOrganism,
  HokkaidoHorseBreedOrganism: AnimalBreeds_HokkaidoHorseBreedOrganism,
  HighlandPonyHorseBreedOrganism: AnimalBreeds_HighlandPonyHorseBreedOrganism,
  GroningenHorseBreedOrganism: AnimalBreeds_GroningenHorseBreedOrganism,
  CubanPintoHorseBreedOrganism: AnimalBreeds_CubanPintoHorseBreedOrganism,
  FleuveHorseBreedOrganism: AnimalBreeds_FleuveHorseBreedOrganism,
  GoldenAmericanSaddlebredHorseBreedOrganism: AnimalBreeds_GoldenAmericanSaddlebredHorseBreedOrganism,
  GidranHorseBreedOrganism: AnimalBreeds_GidranHorseBreedOrganism,
  GelderlandHorseBreedOrganism: AnimalBreeds_GelderlandHorseBreedOrganism,
  GalicianPonyHorseBreedOrganism: AnimalBreeds_GalicianPonyHorseBreedOrganism,
  FriesianHorseBreedOrganism: AnimalBreeds_FriesianHorseBreedOrganism,
  FrederiksborgHorseBreedOrganism: AnimalBreeds_FrederiksborgHorseBreedOrganism,
  FoutaHorseBreedOrganism: AnimalBreeds_FoutaHorseBreedOrganism,
  FloridaCrackerHorseBreedOrganism: AnimalBreeds_FloridaCrackerHorseBreedOrganism,
  GuangxiHorseBreedOrganism: AnimalBreeds_GuangxiHorseBreedOrganism,
  ArdennesHorseBreedOrganism: AnimalBreeds_ArdennesHorseBreedOrganism,
  AmericanWalkingPonyHorseBreedOrganism: AnimalBreeds_AmericanWalkingPonyHorseBreedOrganism,
  AztecaHorseBreedOrganism: AnimalBreeds_AztecaHorseBreedOrganism,
  AmericanCreamDraftHorseBreedOrganism: AnimalBreeds_AmericanCreamDraftHorseBreedOrganism,
  AltaiHorseBreedOrganism: AnimalBreeds_AltaiHorseBreedOrganism,
  AkhalTekeHorseBreedOrganism: AnimalBreeds_AkhalTekeHorseBreedOrganism,
  AbyssinianHorseBreedOrganism: AnimalBreeds_AbyssinianHorseBreedOrganism,
  BhirumPonyHorseBreedOrganism: AnimalBreeds_BhirumPonyHorseBreedOrganism,
  ChejuHorseBreedOrganism: AnimalBreeds_ChejuHorseBreedOrganism,
  CayuseHorseBreedOrganism: AnimalBreeds_CayuseHorseBreedOrganism,
  CaspianHorseBreedOrganism: AnimalBreeds_CaspianHorseBreedOrganism,
  CarthusianHorseBreedOrganism: AnimalBreeds_CarthusianHorseBreedOrganism,
  CampolinaHorseBreedOrganism: AnimalBreeds_CampolinaHorseBreedOrganism,
  ByelorussianHarnessHorseBreedOrganism: AnimalBreeds_ByelorussianHarnessHorseBreedOrganism,
  BudyonnyHorseBreedOrganism: AnimalBreeds_BudyonnyHorseBreedOrganism,
  AustralianBrumbyHorseBreedOrganism: AnimalBreeds_AustralianBrumbyHorseBreedOrganism,
  AustralianStockHorseHorseBreedOrganism: AnimalBreeds_AustralianStockHorseHorseBreedOrganism,
  BasutoPonyHorseBreedOrganism: AnimalBreeds_BasutoPonyHorseBreedOrganism,
  BashkirCurlyHorseBreedOrganism: AnimalBreeds_BashkirCurlyHorseBreedOrganism,
  BashkirHorseBreedOrganism: AnimalBreeds_BashkirHorseBreedOrganism,
  BarbHorseBreedOrganism: AnimalBreeds_BarbHorseBreedOrganism,
  BanEiHorseBreedOrganism: AnimalBreeds_BanEiHorseBreedOrganism,
  CarpathianPonyHorseBreedOrganism: AnimalBreeds_CarpathianPonyHorseBreedOrganism,
  BaluchiHorseBreedOrganism: AnimalBreeds_BaluchiHorseBreedOrganism,
  BalearicHorseBreedOrganism: AnimalBreeds_BalearicHorseBreedOrganism,
  ChileanCorraleroHorseBreedOrganism: AnimalBreeds_ChileanCorraleroHorseBreedOrganism,
  BretonHorseBreedOrganism: AnimalBreeds_BretonHorseBreedOrganism,
  TaishuhHorseBreedOrganism: AnimalBreeds_TaishuhHorseBreedOrganism,
  SwedishWarmbloodHorseBreedOrganism: AnimalBreeds_SwedishWarmbloodHorseBreedOrganism,
  SudanCountryBredHorseBreedOrganism: AnimalBreeds_SudanCountryBredHorseBreedOrganism,
  SpanishNormanHorseBreedOrganism: AnimalBreeds_SpanishNormanHorseBreedOrganism,
  SpanishBarbHorseBreedOrganism: AnimalBreeds_SpanishBarbHorseBreedOrganism,
  SovietHeavyDraftHorseBreedOrganism: AnimalBreeds_SovietHeavyDraftHorseBreedOrganism,
  SorraiaHorseBreedOrganism: AnimalBreeds_SorraiaHorseBreedOrganism,
  SomaliPonyHorseBreedOrganism: AnimalBreeds_SomaliPonyHorseBreedOrganism,
  TerskHorseBreedOrganism: AnimalBreeds_TerskHorseBreedOrganism,
  ShagyaHorseBreedOrganism: AnimalBreeds_ShagyaHorseBreedOrganism,
  SelleFrancaisHorseBreedOrganism: AnimalBreeds_SelleFrancaisHorseBreedOrganism,
  SanheHorseBreedOrganism: AnimalBreeds_SanheHorseBreedOrganism,
  RussianHeavyDraftHorseBreedOrganism: AnimalBreeds_RussianHeavyDraftHorseBreedOrganism,
  RockyMountainHorseHorseBreedOrganism: AnimalBreeds_RockyMountainHorseHorseBreedOrganism,
  RackingHorseHorseBreedOrganism: AnimalBreeds_RackingHorseHorseBreedOrganism,
  QuarterPonyHorseBreedOrganism: AnimalBreeds_QuarterPonyHorseBreedOrganism,
  QuarabHorseBreedOrganism: AnimalBreeds_QuarabHorseBreedOrganism,
  SingleFootingHorseHorseBreedOrganism: AnimalBreeds_SingleFootingHorseHorseBreedOrganism,
  TuyHoaHairlessPigBreedOrganism: AnimalBreeds_TuyHoaHairlessPigBreedOrganism,
  HainanPigBreedOrganism: AnimalBreeds_HainanPigBreedOrganism,
  SinoVietnamesePigBreedOrganism: AnimalBreeds_SinoVietnamesePigBreedOrganism,
  BoXuPigBreedOrganism: AnimalBreeds_BoXuPigBreedOrganism,
  ThuocNhieuPigBreedOrganism: AnimalBreeds_ThuocNhieuPigBreedOrganism,
  BurmesePigBreedOrganism: AnimalBreeds_BurmesePigBreedOrganism,
  ChinPigBreedOrganism: AnimalBreeds_ChinPigBreedOrganism,
  SiamesePigBreedOrganism: AnimalBreeds_SiamesePigBreedOrganism,
  HailumPigBreedOrganism: AnimalBreeds_HailumPigBreedOrganism,
  KwaiPigBreedOrganism: AnimalBreeds_KwaiPigBreedOrganism,
  RaadPigBreedOrganism: AnimalBreeds_RaadPigBreedOrganism,
  AkhaPigBreedOrganism: AnimalBreeds_AkhaPigBreedOrganism,
  SouthChinaPigBreedOrganism: AnimalBreeds_SouthChinaPigBreedOrganism,
  SouthChinaBlackPigBreedOrganism: AnimalBreeds_SouthChinaBlackPigBreedOrganism,
  BalinesePigBreedOrganism: AnimalBreeds_BalinesePigBreedOrganism,
  DianiPigBreedOrganism: AnimalBreeds_DianiPigBreedOrganism,
  KamanPigBreedOrganism: AnimalBreeds_KamanPigBreedOrganism,
  AshantiDwarfPigBreedOrganism: AnimalBreeds_AshantiDwarfPigBreedOrganism,
  KoronadalPigBreedOrganism: AnimalBreeds_KoronadalPigBreedOrganism,
  OhminiPigBreedOrganism: AnimalBreeds_OhminiPigBreedOrganism,
  ClawnPigBreedOrganism: AnimalBreeds_ClawnPigBreedOrganism,
  SusScrofaDomesticPigXJapaneseWildBoarIntragenusHybridOrganism: AnimalBreeds_SusScrofaDomesticPigXJapaneseWildBoarIntragenusHybridOrganism,
  KangarooIslandPigBreedOrganism: AnimalBreeds_KangarooIslandPigBreedOrganism,
  CaptainCookerPigBreedOrganism: AnimalBreeds_CaptainCookerPigBreedOrganism,
  WestAfricanPigBreedOrganism: AnimalBreeds_WestAfricanPigBreedOrganism,
  NigerianPigBreedOrganism: AnimalBreeds_NigerianPigBreedOrganism,
  BakosiPigBreedOrganism: AnimalBreeds_BakosiPigBreedOrganism,
  WindsnyerPigBreedOrganism: AnimalBreeds_WindsnyerPigBreedOrganism,
  KolbroekPigBreedOrganism: AnimalBreeds_KolbroekPigBreedOrganism,
  SouthAfricanLandracePigBreedOrganism: AnimalBreeds_SouthAfricanLandracePigBreedOrganism,
  BulgarianWhitePigBreedOrganism: AnimalBreeds_BulgarianWhitePigBreedOrganism,
  BulgarianLandracePigBreedOrganism: AnimalBreeds_BulgarianLandracePigBreedOrganism,
  DanubeWhitePigBreedOrganism: AnimalBreeds_DanubeWhitePigBreedOrganism,
  DermantsiPiedPigBreedOrganism: AnimalBreeds_DermantsiPiedPigBreedOrganism,
  RomanianNativeStocliPigBreedOrganism: AnimalBreeds_RomanianNativeStocliPigBreedOrganism,
  RomanianNativeBaltaretPigBreedOrganism: AnimalBreeds_RomanianNativeBaltaretPigBreedOrganism,
  BanatWhitePigBreedOrganism: AnimalBreeds_BanatWhitePigBreedOrganism,
  BaznaPigBreedOrganism: AnimalBreeds_BaznaPigBreedOrganism,
  DobrogeaBlackPigBreedOrganism: AnimalBreeds_DobrogeaBlackPigBreedOrganism,
  StreiPigBreedOrganism: AnimalBreeds_StreiPigBreedOrganism,
  RomanianLargeWhitePigBreedOrganism: AnimalBreeds_RomanianLargeWhitePigBreedOrganism,
  RomanianMeatPigPigBreedOrganism: AnimalBreeds_RomanianMeatPigPigBreedOrganism,
  GurktalPigBreedOrganism: AnimalBreeds_GurktalPigBreedOrganism,
  BlackSlavonianPigBreedOrganism: AnimalBreeds_BlackSlavonianPigBreedOrganism,
  ResavaPigBreedOrganism: AnimalBreeds_ResavaPigBreedOrganism,
  MoravaPigBreedOrganism: AnimalBreeds_MoravaPigBreedOrganism,
  DzumaliaPigBreedOrganism: AnimalBreeds_DzumaliaPigBreedOrganism,
  MacedonianPigBreedOrganism: AnimalBreeds_MacedonianPigBreedOrganism,
  AlbanianNativePigBreedOrganism: AnimalBreeds_AlbanianNativePigBreedOrganism,
  ShkodraPigBreedOrganism: AnimalBreeds_ShkodraPigBreedOrganism,
  SlovenianWhitePigBreedOrganism: AnimalBreeds_SlovenianWhitePigBreedOrganism,
  SuboticaWhitePigBreedOrganism: AnimalBreeds_SuboticaWhitePigBreedOrganism,
  PresticePigBreedOrganism: AnimalBreeds_PresticePigBreedOrganism,
  SlovakianBlackPiedPigBreedOrganism: AnimalBreeds_SlovakianBlackPiedPigBreedOrganism,
  CzechImprovedWhitePigBreedOrganism: AnimalBreeds_CzechImprovedWhitePigBreedOrganism,
  MoravianLargeYorkshirePigBreedOrganism: AnimalBreeds_MoravianLargeYorkshirePigBreedOrganism,
  SlovakianWhitePigBreedOrganism: AnimalBreeds_SlovakianWhitePigBreedOrganism,
  Slovhyb1PigBreedOrganism: AnimalBreeds_Slovhyb1PigBreedOrganism,
  NitraHybridPigBreedOrganism: AnimalBreeds_NitraHybridPigBreedOrganism,
  SyntheticSL98PigBreedOrganism: AnimalBreeds_SyntheticSL98PigBreedOrganism,
  SL96PigBreedOrganism: AnimalBreeds_SL96PigBreedOrganism,
  CzechMeatPigBreedOrganism: AnimalBreeds_CzechMeatPigBreedOrganism,
  CzechMiniaturePigBreedOrganism: AnimalBreeds_CzechMiniaturePigBreedOrganism,
  SmallPolishPrickEaredPigBreedOrganism: AnimalBreeds_SmallPolishPrickEaredPigBreedOrganism,
  PolesianPigBreedOrganism: AnimalBreeds_PolesianPigBreedOrganism,
  NadbuzanskaPigBreedOrganism: AnimalBreeds_NadbuzanskaPigBreedOrganism,
  SarnyPigBreedOrganism: AnimalBreeds_SarnyPigBreedOrganism,
  KrolevetsPigBreedOrganism: AnimalBreeds_KrolevetsPigBreedOrganism,
  PolishMarshPigBreedOrganism: AnimalBreeds_PolishMarshPigBreedOrganism,
  LargePolishLongEaredPigBreedOrganism: AnimalBreeds_LargePolishLongEaredPigBreedOrganism,
  HerensCattleBreedOrganism: AnimalBreeds_HerensCattleBreedOrganism,
  HinterwaldCattleBreedOrganism: AnimalBreeds_HinterwaldCattleBreedOrganism,
  HungarianGrayCattleBreedOrganism: AnimalBreeds_HungarianGrayCattleBreedOrganism,
  IcelandicCattleBreedOrganism: AnimalBreeds_IcelandicCattleBreedOrganism,
  IllawarraCattleBreedOrganism: AnimalBreeds_IllawarraCattleBreedOrganism,
  IrishMoiledCattleBreedOrganism: AnimalBreeds_IrishMoiledCattleBreedOrganism,
  IsraeliHolsteinCattleBreedOrganism: AnimalBreeds_IsraeliHolsteinCattleBreedOrganism,
  IstobenCattleBreedOrganism: AnimalBreeds_IstobenCattleBreedOrganism,
  JaulanCattleBreedOrganism: AnimalBreeds_JaulanCattleBreedOrganism,
  KazakhCattleBreedOrganism: AnimalBreeds_KazakhCattleBreedOrganism,
  KerryCattleBreedOrganism: AnimalBreeds_KerryCattleBreedOrganism,
  KholmogoryCattleBreedOrganism: AnimalBreeds_KholmogoryCattleBreedOrganism,
  LatvianBrownCattleBreedOrganism: AnimalBreeds_LatvianBrownCattleBreedOrganism,
  LincolnRedShorthornCattleBreedOrganism: AnimalBreeds_LincolnRedShorthornCattleBreedOrganism,
  LithuanianRedCattleBreedOrganism: AnimalBreeds_LithuanianRedCattleBreedOrganism,
  MashonaCattleBreedOrganism: AnimalBreeds_MashonaCattleBreedOrganism,
  MilkingDevonCattleBreedOrganism: AnimalBreeds_MilkingDevonCattleBreedOrganism,
  MirandesaCattleBreedOrganism: AnimalBreeds_MirandesaCattleBreedOrganism,
  MixedDairyCattleBreedOrganism: AnimalBreeds_MixedDairyCattleBreedOrganism,
  MongolianCattleBreedOrganism: AnimalBreeds_MongolianCattleBreedOrganism,
  MoruchaCattleBreedOrganism: AnimalBreeds_MoruchaCattleBreedOrganism,
  KurdiCattleBreedOrganism: AnimalBreeds_KurdiCattleBreedOrganism,
  NQuoteDamaCattleBreedOrganism: AnimalBreeds_NQuoteDamaCattleBreedOrganism,
  NorwegianRedCattleBreedOrganism: AnimalBreeds_NorwegianRedCattleBreedOrganism,
  ParthenaisCattleBreedOrganism: AnimalBreeds_ParthenaisCattleBreedOrganism,
  PolishRedCattleBreedOrganism: AnimalBreeds_PolishRedCattleBreedOrganism,
  RtienGrayCattleBreedOrganism: AnimalBreeds_RtienGrayCattleBreedOrganism,
  RedAndWhiteCattleBreedOrganism: AnimalBreeds_RedAndWhiteCattleBreedOrganism,
  RedAngusCattleBreedOrganism: AnimalBreeds_RedAngusCattleBreedOrganism,
  RedPolledStlandCattleBreedOrganism: AnimalBreeds_RedPolledStlandCattleBreedOrganism,
  RedSteppeCattleBreedOrganism: AnimalBreeds_RedSteppeCattleBreedOrganism,
  ReggianaCattleBreedOrganism: AnimalBreeds_ReggianaCattleBreedOrganism,
  RetintaCattleBreedOrganism: AnimalBreeds_RetintaCattleBreedOrganism,
  RomosinuanoCattleBreedOrganism: AnimalBreeds_RomosinuanoCattleBreedOrganism,
  RussianBlackPiedCattleBreedOrganism: AnimalBreeds_RussianBlackPiedCattleBreedOrganism,
  RX3CattleBreedOrganism: AnimalBreeds_RX3CattleBreedOrganism,
  SalornCattleBreedOrganism: AnimalBreeds_SalornCattleBreedOrganism,
  MurbodenCattleBreedOrganism: AnimalBreeds_MurbodenCattleBreedOrganism,
  SanMartineroCattleBreedOrganism: AnimalBreeds_SanMartineroCattleBreedOrganism,
  SarabiCattleBreedOrganism: AnimalBreeds_SarabiCattleBreedOrganism,
  SharabiCattleBreedOrganism: AnimalBreeds_SharabiCattleBreedOrganism,
  ShetlandCattleBreedOrganism: AnimalBreeds_ShetlandCattleBreedOrganism,
  SimbrahCattleBreedOrganism: AnimalBreeds_SimbrahCattleBreedOrganism,
  SouthDevonCattleBreedOrganism: AnimalBreeds_SouthDevonCattleBreedOrganism,
  SuffolkCattleBreedOrganism: AnimalBreeds_SuffolkCattleBreedOrganism,
  SussexCattleBreedOrganism: AnimalBreeds_SussexCattleBreedOrganism,
  SwedishRedPolledCattleBreedOrganism: AnimalBreeds_SwedishRedPolledCattleBreedOrganism,
  TelemarkCattleBreedOrganism: AnimalBreeds_TelemarkCattleBreedOrganism,
  TexasLonghornCattleBreedOrganism: AnimalBreeds_TexasLonghornCattleBreedOrganism,
  TexonCattleBreedOrganism: AnimalBreeds_TexonCattleBreedOrganism,
  VestlandFjordCattleBreedOrganism: AnimalBreeds_VestlandFjordCattleBreedOrganism,
  VestlandRedPolledCattleBreedOrganism: AnimalBreeds_VestlandRedPolledCattleBreedOrganism,
  WagyuCattleBreedOrganism: AnimalBreeds_WagyuCattleBreedOrganism,
  WhiteCceresCattleBreedOrganism: AnimalBreeds_WhiteCceresCattleBreedOrganism,
  XinjiangBrownCattleBreedOrganism: AnimalBreeds_XinjiangBrownCattleBreedOrganism,
  YanbianCattleBreedOrganism: AnimalBreeds_YanbianCattleBreedOrganism,
  ZaobeiCattleBreedOrganism: AnimalBreeds_ZaobeiCattleBreedOrganism,
  ZavotCattleBreedOrganism: AnimalBreeds_ZavotCattleBreedOrganism,
  ZnamenskCattleBreedOrganism: AnimalBreeds_ZnamenskCattleBreedOrganism,
  AlistanaSanabresaCattleBreedOrganism: AnimalBreeds_AlistanaSanabresaCattleBreedOrganism,
  AndalusianBlondCattleBreedOrganism: AnimalBreeds_AndalusianBlondCattleBreedOrganism,
  AostaBlackPiedCattleBreedOrganism: AnimalBreeds_AostaBlackPiedCattleBreedOrganism,
  AostaChestnutCattleBreedOrganism: AnimalBreeds_AostaChestnutCattleBreedOrganism,
  AostaRedPiedCattleBreedOrganism: AnimalBreeds_AostaRedPiedCattleBreedOrganism,
  AracenaCattleBreedOrganism: AnimalBreeds_AracenaCattleBreedOrganism,
  ArgentineFriesianCattleBreedOrganism: AnimalBreeds_ArgentineFriesianCattleBreedOrganism,
  ArmoricanCattleBreedOrganism: AnimalBreeds_ArmoricanCattleBreedOrganism,
  ArouquesaCattleBreedOrganism: AnimalBreeds_ArouquesaCattleBreedOrganism,
  AureEtSaintGironsCattleBreedOrganism: AnimalBreeds_AureEtSaintGironsCattleBreedOrganism,
  AustralianWhiteCattleBreedOrganism: AnimalBreeds_AustralianWhiteCattleBreedOrganism,
  AustrianSimmentalCattleBreedOrganism: AnimalBreeds_AustrianSimmentalCattleBreedOrganism,
  AustrianYellowCattleBreedOrganism: AnimalBreeds_AustrianYellowCattleBreedOrganism,
  AvetonouCattleBreedOrganism: AnimalBreeds_AvetonouCattleBreedOrganism,
  AvilenaCattleBreedOrganism: AnimalBreeds_AvilenaCattleBreedOrganism,
  AvilenaBlackIberianCattleBreedOrganism: AnimalBreeds_AvilenaBlackIberianCattleBreedOrganism,
  BakosiCattleBreedOrganism: AnimalBreeds_BakosiCattleBreedOrganism,
  BakwiriCattleBreedOrganism: AnimalBreeds_BakwiriCattleBreedOrganism,
  BalticBlackPiedCattleBreedOrganism: AnimalBreeds_BalticBlackPiedCattleBreedOrganism,
  BaouleCattleBreedOrganism: AnimalBreeds_BaouleCattleBreedOrganism,
  BarrosaCattleBreedOrganism: AnimalBreeds_BarrosaCattleBreedOrganism,
  BarrosoCattleBreedOrganism: AnimalBreeds_BarrosoCattleBreedOrganism,
  BearnaisCattleBreedOrganism: AnimalBreeds_BearnaisCattleBreedOrganism,
  BeefShorthornCattleBreedOrganism: AnimalBreeds_BeefShorthornCattleBreedOrganism,
  BeefSyntheticCattleBreedOrganism: AnimalBreeds_BeefSyntheticCattleBreedOrganism,
  BeijingBlackPiedCattleBreedOrganism: AnimalBreeds_BeijingBlackPiedCattleBreedOrganism,
  BeiroaCattleBreedOrganism: AnimalBreeds_BeiroaCattleBreedOrganism,
  BelgianBlackPiedHolsteincattleBreedOrganism: AnimalBreeds_BelgianBlackPiedHolsteincattleBreedOrganism,
  BelgianRedPiedCattleBreedOrganism: AnimalBreeds_BelgianRedPiedCattleBreedOrganism,
  BelgianWhiteAndRedCattleBreedOrganism: AnimalBreeds_BelgianWhiteAndRedCattleBreedOrganism,
  BeltedWelshCattleBreedOrganism: AnimalBreeds_BeltedWelshCattleBreedOrganism,
  BestuzhevCattleBreedOrganism: AnimalBreeds_BestuzhevCattleBreedOrganism,
  BetizuakCattleBreedOrganism: AnimalBreeds_BetizuakCattleBreedOrganism,
  BlackBaldyCattleBreedOrganism: AnimalBreeds_BlackBaldyCattleBreedOrganism,
  BlackForestCattleBreedOrganism: AnimalBreeds_BlackForestCattleBreedOrganism,
  BlackIberianCattleBreedOrganism: AnimalBreeds_BlackIberianCattleBreedOrganism,
  NorthernBlueCattleBreedOrganism: AnimalBreeds_NorthernBlueCattleBreedOrganism,
  BragadoDoSorraiaCattleBreedOrganism: AnimalBreeds_BragadoDoSorraiaCattleBreedOrganism,
  BragancaCattleBreedOrganism: AnimalBreeds_BragancaCattleBreedOrganism,
  BrandroodLjsselveeCattleBreedOrganism: AnimalBreeds_BrandroodLjsselveeCattleBreedOrganism,
  BrazilianPolledCattleBreedOrganism: AnimalBreeds_BrazilianPolledCattleBreedOrganism,
  BretonBlackPiedCattleBreedOrganism: AnimalBreeds_BretonBlackPiedCattleBreedOrganism,
  BrownAtlasCattleBreedOrganism: AnimalBreeds_BrownAtlasCattleBreedOrganism,
  BulgarianBrownCattleBreedOrganism: AnimalBreeds_BulgarianBrownCattleBreedOrganism,
  BulgarianRedCattleBreedOrganism: AnimalBreeds_BulgarianRedCattleBreedOrganism,
  BurlinaCattleBreedOrganism: AnimalBreeds_BurlinaCattleBreedOrganism,
  BurwashCattleBreedOrganism: AnimalBreeds_BurwashCattleBreedOrganism,
  ByelorussianRedCattleBreedOrganism: AnimalBreeds_ByelorussianRedCattleBreedOrganism,
  ByelorussianSyntheticCattleBreedOrganism: AnimalBreeds_ByelorussianSyntheticCattleBreedOrganism,
  CabanninaCattleBreedOrganism: AnimalBreeds_CabanninaCattleBreedOrganism,
  CaldeanoCattleBreedOrganism: AnimalBreeds_CaldeanoCattleBreedOrganism,
  CaldelanaCattleBreedOrganism: AnimalBreeds_CaldelanaCattleBreedOrganism,
  CalvanaCattleBreedOrganism: AnimalBreeds_CalvanaCattleBreedOrganism,
  CamargueCattleBreedOrganism: AnimalBreeds_CamargueCattleBreedOrganism,
  CambodianCattleBreedOrganism: AnimalBreeds_CambodianCattleBreedOrganism,
  CaracuCattleBreedOrganism: AnimalBreeds_CaracuCattleBreedOrganism,
  CarpathianBrownCattleBreedOrganism: AnimalBreeds_CarpathianBrownCattleBreedOrganism,
  CasanarenoCattleBreedOrganism: AnimalBreeds_CasanarenoCattleBreedOrganism,
  CentralRussianBlackPiedCattleBreedOrganism: AnimalBreeds_CentralRussianBlackPiedCattleBreedOrganism,
  ChaouiaCattleBreedOrganism: AnimalBreeds_ChaouiaCattleBreedOrganism,
  CharollandaisCattleBreedOrganism: AnimalBreeds_CharollandaisCattleBreedOrganism,
  CharSwissCattleBreedOrganism: AnimalBreeds_CharSwissCattleBreedOrganism,
  KoreanBlackCattleBreedOrganism: AnimalBreeds_KoreanBlackCattleBreedOrganism,
  ChesiCattleBreedOrganism: AnimalBreeds_ChesiCattleBreedOrganism,
  CheurfaCattleBreedOrganism: AnimalBreeds_CheurfaCattleBreedOrganism,
  ChifordCattleBreedOrganism: AnimalBreeds_ChifordCattleBreedOrganism,
  ChimaineCattleBreedOrganism: AnimalBreeds_ChimaineCattleBreedOrganism,
  ChinampoCattleBreedOrganism: AnimalBreeds_ChinampoCattleBreedOrganism,
  CildirCattleBreedOrganism: AnimalBreeds_CildirCattleBreedOrganism,
  COOPELSO93CattleBreedOrganism: AnimalBreeds_COOPELSO93CattleBreedOrganism,
  ThraceCattleBreedOrganism: AnimalBreeds_ThraceCattleBreedOrganism,
  CorsicanCattleBreedOrganism: AnimalBreeds_CorsicanCattleBreedOrganism,
  CretanLowlandCattleBreedOrganism: AnimalBreeds_CretanLowlandCattleBreedOrganism,
  CretanMountainCattleBreedOrganism: AnimalBreeds_CretanMountainCattleBreedOrganism,
  CroatianRedCattleBreedOrganism: AnimalBreeds_CroatianRedCattleBreedOrganism,
  CukurovaCattleBreedOrganism: AnimalBreeds_CukurovaCattleBreedOrganism,
  CurraleiroCattleBreedOrganism: AnimalBreeds_CurraleiroCattleBreedOrganism,
  CyprusCattleBreedOrganism: AnimalBreeds_CyprusCattleBreedOrganism,
  CzechPiedCattleBreedOrganism: AnimalBreeds_CzechPiedCattleBreedOrganism,
  DagestanMountainCattleBreedOrganism: AnimalBreeds_DagestanMountainCattleBreedOrganism,
  DairyShorthornCattleBreedOrganism: AnimalBreeds_DairyShorthornCattleBreedOrganism,
  DairySyntheticCattleBreedOrganism: AnimalBreeds_DairySyntheticCattleBreedOrganism,
  DanishRedPiedCattleBreedOrganism: AnimalBreeds_DanishRedPiedCattleBreedOrganism,
  DengchuanCattleBreedOrganism: AnimalBreeds_DengchuanCattleBreedOrganism,
  DexterKerryCattleBreedOrganism: AnimalBreeds_DexterKerryCattleBreedOrganism,
  DoranCattleBreedOrganism: AnimalBreeds_DoranCattleBreedOrganism,
  DornaCattleBreedOrganism: AnimalBreeds_DornaCattleBreedOrganism,
  DortyolCattleBreedOrganism: AnimalBreeds_DortyolCattleBreedOrganism,
  EastAnatolianRedCattleBreedOrganism: AnimalBreeds_EastAnatolianRedCattleBreedOrganism,
  EastFinnishCattleBreedOrganism: AnimalBreeds_EastFinnishCattleBreedOrganism,
  EastMacedonianCattleBreedOrganism: AnimalBreeds_EastMacedonianCattleBreedOrganism,
  EpirusCattleBreedOrganism: AnimalBreeds_EpirusCattleBreedOrganism,
  EstonianBlackPiedCattleBreedOrganism: AnimalBreeds_EstonianBlackPiedCattleBreedOrganism,
  FerrandaisCattleBreedOrganism: AnimalBreeds_FerrandaisCattleBreedOrganism,
  FinnishAyrshireCattleBreedOrganism: AnimalBreeds_FinnishAyrshireCattleBreedOrganism,
  FlemishCattleBreedOrganism: AnimalBreeds_FlemishCattleBreedOrganism,
  RedFlemishCattleBreedOrganism: AnimalBreeds_RedFlemishCattleBreedOrganism,
  FortCrossCattleBreedOrganism: AnimalBreeds_FortCrossCattleBreedOrganism,
  FratiCattleBreedOrganism: AnimalBreeds_FratiCattleBreedOrganism,
  EstonianNativeCattleBreedOrganism: AnimalBreeds_EstonianNativeCattleBreedOrganism,
  FaeroesCattleBreedOrganism: AnimalBreeds_FaeroesCattleBreedOrganism,
  FrenchBrownCattleBreedOrganism: AnimalBreeds_FrenchBrownCattleBreedOrganism,
  FrijolilloCattleBreedOrganism: AnimalBreeds_FrijolilloCattleBreedOrganism,
  FRSCattleBreedOrganism: AnimalBreeds_FRSCattleBreedOrganism,
  GackoCattleBreedOrganism: AnimalBreeds_GackoCattleBreedOrganism,
  GadoDaTerraCattleBreedOrganism: AnimalBreeds_GadoDaTerraCattleBreedOrganism,
  GeorgianMountainCattleBreedOrganism: AnimalBreeds_GeorgianMountainCattleBreedOrganism,
  GermanBlackPiedCattleBreedOrganism: AnimalBreeds_GermanBlackPiedCattleBreedOrganism,
  GermanBlackPiedDairyCattleBreedOrganism: AnimalBreeds_GermanBlackPiedDairyCattleBreedOrganism,
  PechoraCattleBreedOrganism: AnimalBreeds_PechoraCattleBreedOrganism,
  PeeWeeCattleBreedOrganism: AnimalBreeds_PeeWeeCattleBreedOrganism,
  PeloponnesusCattleBreedOrganism: AnimalBreeds_PeloponnesusCattleBreedOrganism,
  PesterCattleBreedOrganism: AnimalBreeds_PesterCattleBreedOrganism,
  PieRougeDeLQuoteEstCattleBreedOrganism: AnimalBreeds_PieRougeDeLQuoteEstCattleBreedOrganism,
  PisanaCattleBreedOrganism: AnimalBreeds_PisanaCattleBreedOrganism,
  GermanBrownCattleBreedOrganism: AnimalBreeds_GermanBrownCattleBreedOrganism,
  GermanShorthornCattleBreedOrganism: AnimalBreeds_GermanShorthornCattleBreedOrganism,
  GhanaShorthornCattleBreedOrganism: AnimalBreeds_GhanaShorthornCattleBreedOrganism,
  GlanDonnersbergCattleBreedOrganism: AnimalBreeds_GlanDonnersbergCattleBreedOrganism,
  GoleCattleBreedOrganism: AnimalBreeds_GoleCattleBreedOrganism,
  GolpayeganiCattleBreedOrganism: AnimalBreeds_GolpayeganiCattleBreedOrganism,
  GorbatovRedCattleBreedOrganism: AnimalBreeds_GorbatovRedCattleBreedOrganism,
  GorynCattleBreedOrganism: AnimalBreeds_GorynCattleBreedOrganism,
  GreaterCaucasusCattleBreedOrganism: AnimalBreeds_GreaterCaucasusCattleBreedOrganism,
  PolishBlackAndWhiteLowlandCattleBreedOrganism: AnimalBreeds_PolishBlackAndWhiteLowlandCattleBreedOrganism,
  PolishSimmentalCattleBreedOrganism: AnimalBreeds_PolishSimmentalCattleBreedOrganism,
  PolledJerseyCattleBreedOrganism: AnimalBreeds_PolledJerseyCattleBreedOrganism,
  PolledLincolnRedCattleBreedOrganism: AnimalBreeds_PolledLincolnRedCattleBreedOrganism,
  PolledShorthornUnitedStatesOfAmericaCattleBreedOrganism: AnimalBreeds_PolledShorthornUnitedStatesOfAmericaCattleBreedOrganism,
  PolledSimmentalCattleBreedOrganism: AnimalBreeds_PolledSimmentalCattleBreedOrganism,
  GreekShorthornCattleBreedOrganism: AnimalBreeds_GreekShorthornCattleBreedOrganism,
  GreekSteppeCattleBreedOrganism: AnimalBreeds_GreekSteppeCattleBreedOrganism,
  GrayAlpineCattleBreedOrganism: AnimalBreeds_GrayAlpineCattleBreedOrganism,
  GuadianaSpottedCattleBreedOrganism: AnimalBreeds_GuadianaSpottedCattleBreedOrganism,
  GuelmaCattleBreedOrganism: AnimalBreeds_GuelmaCattleBreedOrganism,
  HarzRedCattleBreedOrganism: AnimalBreeds_HarzRedCattleBreedOrganism,
  HawaiianWildCattleBreedOrganism: AnimalBreeds_HawaiianWildCattleBreedOrganism,
  HerelandCattleBreedOrganism: AnimalBreeds_HerelandCattleBreedOrganism,
  HolgusCattleBreedOrganism: AnimalBreeds_HolgusCattleBreedOrganism,
  HrbineckyCattleBreedOrganism: AnimalBreeds_HrbineckyCattleBreedOrganism,
  PolledSussexCattleBreedOrganism: AnimalBreeds_PolledSussexCattleBreedOrganism,
  PolledWelshBlackCattleBreedOrganism: AnimalBreeds_PolledWelshBlackCattleBreedOrganism,
  PontremoleseCattleBreedOrganism: AnimalBreeds_PontremoleseCattleBreedOrganism,
  PretaCattleBreedOrganism: AnimalBreeds_PretaCattleBreedOrganism,
  PuertoRicanCriolloCattleBreedOrganism: AnimalBreeds_PuertoRicanCriolloCattleBreedOrganism,
  PyreneanCattleBreedOrganism: AnimalBreeds_PyreneanCattleBreedOrganism,
  HuertanaCattleBreedOrganism: AnimalBreeds_HuertanaCattleBreedOrganism,
  HungarianPiedCattleBreedOrganism: AnimalBreeds_HungarianPiedCattleBreedOrganism,
  HungarofriesCattleBreedOrganism: AnimalBreeds_HungarofriesCattleBreedOrganism,
  ImprovedRodopiCattleBreedOrganism: AnimalBreeds_ImprovedRodopiCattleBreedOrganism,
  INRA95CattleBreedOrganism: AnimalBreeds_INRA95CattleBreedOrganism,
  ItalianBrownCattleBreedOrganism: AnimalBreeds_ItalianBrownCattleBreedOrganism,
  ItalianRedPiedCattleBreedOrganism: AnimalBreeds_ItalianRedPiedCattleBreedOrganism,
  JapaneseBlackCattleBreedOrganism: AnimalBreeds_JapaneseBlackCattleBreedOrganism,
  JapaneseBrownCattleBreedOrganism: AnimalBreeds_JapaneseBrownCattleBreedOrganism,
  JapanesePollCattleBreedOrganism: AnimalBreeds_JapanesePollCattleBreedOrganism,
  QinchuanCattleBreedOrganism: AnimalBreeds_QinchuanCattleBreedOrganism,
  RamoGrandeCattleBreedOrganism: AnimalBreeds_RamoGrandeCattleBreedOrganism,
  RandallLinebackCattleBreedOrganism: AnimalBreeds_RandallLinebackCattleBreedOrganism,
  RedGallowayCattleBreedOrganism: AnimalBreeds_RedGallowayCattleBreedOrganism,
  RegusCattleBreedOrganism: AnimalBreeds_RegusCattleBreedOrganism,
  RendenaCattleBreedOrganism: AnimalBreeds_RendenaCattleBreedOrganism,
  JapaneseShorthornCattleBreedOrganism: AnimalBreeds_JapaneseShorthornCattleBreedOrganism,
  JarmelistaCattleBreedOrganism: AnimalBreeds_JarmelistaCattleBreedOrganism,
  KabyleCattleBreedOrganism: AnimalBreeds_KabyleCattleBreedOrganism,
  KapsikiCattleBreedOrganism: AnimalBreeds_KapsikiCattleBreedOrganism,
  KateriniCattleBreedOrganism: AnimalBreeds_KateriniCattleBreedOrganism,
  KenranCattleBreedOrganism: AnimalBreeds_KenranCattleBreedOrganism,
  KhevsurianCattleBreedOrganism: AnimalBreeds_KhevsurianCattleBreedOrganism,
  KilisCattleBreedOrganism: AnimalBreeds_KilisCattleBreedOrganism,
  KochiCattleBreedOrganism: AnimalBreeds_KochiCattleBreedOrganism,
  KoreanNativeCattleBreedOrganism: AnimalBreeds_KoreanNativeCattleBreedOrganism,
  RhaetianGrayCattleBreedOrganism: AnimalBreeds_RhaetianGrayCattleBreedOrganism,
  RioLimonDairyCriolloCattleBreedOrganism: AnimalBreeds_RioLimonDairyCriolloCattleBreedOrganism,
  RodopiCattleBreedOrganism: AnimalBreeds_RodopiCattleBreedOrganism,
  RomanianRedCattleBreedOrganism: AnimalBreeds_RomanianRedCattleBreedOrganism,
  RomanianBrownCattleBreedOrganism: AnimalBreeds_RomanianBrownCattleBreedOrganism,
  RussianBrownCattleBreedOrganism: AnimalBreeds_RussianBrownCattleBreedOrganism,
  KostromaCattleBreedOrganism: AnimalBreeds_KostromaCattleBreedOrganism,
  KravarskyCattleBreedOrganism: AnimalBreeds_KravarskyCattleBreedOrganism,
  KuchinoshimaCattleBreedOrganism: AnimalBreeds_KuchinoshimaCattleBreedOrganism,
  MurrayGrayCattleBreedOrganism: AnimalBreeds_MurrayGrayCattleBreedOrganism,
  AustralianShorthornCattleBreedOrganism: AnimalBreeds_AustralianShorthornCattleBreedOrganism,
  KumamotoCattleBreedOrganism: AnimalBreeds_KumamotoCattleBreedOrganism,
  LaguneCattleBreedOrganism: AnimalBreeds_LaguneCattleBreedOrganism,
  LakenvelderCattleBreedOrganism: AnimalBreeds_LakenvelderCattleBreedOrganism,
  LatvianBlueRoanCattleBreedOrganism: AnimalBreeds_LatvianBlueRoanCattleBreedOrganism,
  LaVelasquezCattleBreedOrganism: AnimalBreeds_LaVelasquezCattleBreedOrganism,
  SardinianCattleBreedOrganism: AnimalBreeds_SardinianCattleBreedOrganism,
  SardinianBrownCattleBreedOrganism: AnimalBreeds_SardinianBrownCattleBreedOrganism,
  SavinjaGrayCattleBreedOrganism: AnimalBreeds_SavinjaGrayCattleBreedOrganism,
  SayaguesaCattleBreedOrganism: AnimalBreeds_SayaguesaCattleBreedOrganism,
  SeferihisarCattleBreedOrganism: AnimalBreeds_SeferihisarCattleBreedOrganism,
  ShkodraRedCattleBreedOrganism: AnimalBreeds_ShkodraRedCattleBreedOrganism,
  LebaneseCattleBreedOrganism: AnimalBreeds_LebaneseCattleBreedOrganism,
  LebedinCattleBreedOrganism: AnimalBreeds_LebedinCattleBreedOrganism,
  LesserCaucasusCattleBreedOrganism: AnimalBreeds_LesserCaucasusCattleBreedOrganism,
  LiberianDwarfCattleBreedOrganism: AnimalBreeds_LiberianDwarfCattleBreedOrganism,
  LibyanCattleBreedOrganism: AnimalBreeds_LibyanCattleBreedOrganism,
  LimCattleBreedOrganism: AnimalBreeds_LimCattleBreedOrganism,
  LimianaCattleBreedOrganism: AnimalBreeds_LimianaCattleBreedOrganism,
  LimpurgerCattleBreedOrganism: AnimalBreeds_LimpurgerCattleBreedOrganism,
  LobiCattleBreedOrganism: AnimalBreeds_LobiCattleBreedOrganism,
  LourdaisCattleBreedOrganism: AnimalBreeds_LourdaisCattleBreedOrganism,
  SlovakianPiedCattleBreedOrganism: AnimalBreeds_SlovakianPiedCattleBreedOrganism,
  SlovakianPinzgauCattleBreedOrganism: AnimalBreeds_SlovakianPinzgauCattleBreedOrganism,
  SlovenianBrownCattleBreedOrganism: AnimalBreeds_SlovenianBrownCattleBreedOrganism,
  SombaCattleBreedOrganism: AnimalBreeds_SombaCattleBreedOrganism,
  SouthAfricanBrownSwissCattleBreedOrganism: AnimalBreeds_SouthAfricanBrownSwissCattleBreedOrganism,
  SouthAnatolianRedCattleBreedOrganism: AnimalBreeds_SouthAnatolianRedCattleBreedOrganism,
  LucernaCattleBreedOrganism: AnimalBreeds_LucernaCattleBreedOrganism,
  LuxiCattleBreedOrganism: AnimalBreeds_LuxiCattleBreedOrganism,
  MacedonianBusaCattleBreedOrganism: AnimalBreeds_MacedonianBusaCattleBreedOrganism,
  MakaweliCattleBreedOrganism: AnimalBreeds_MakaweliCattleBreedOrganism,
  MarinhoaCattleBreedOrganism: AnimalBreeds_MarinhoaCattleBreedOrganism,
  MaronesaCattleBreedOrganism: AnimalBreeds_MaronesaCattleBreedOrganism,
  MazuryCattleBreedOrganism: AnimalBreeds_MazuryCattleBreedOrganism,
  MessaoriaCattleBreedOrganism: AnimalBreeds_MessaoriaCattleBreedOrganism,
  MetohijaRedCattleBreedOrganism: AnimalBreeds_MetohijaRedCattleBreedOrganism,
  MingrelianRedCattleBreedOrganism: AnimalBreeds_MingrelianRedCattleBreedOrganism,
  SouthernUkrainianCattleBreedOrganism: AnimalBreeds_SouthernUkrainianCattleBreedOrganism,
  SpanishBrownAlpineCattleBreedOrganism: AnimalBreeds_SpanishBrownAlpineCattleBreedOrganism,
  SuksunCattleBreedOrganism: AnimalBreeds_SuksunCattleBreedOrganism,
  SwissBlackPiedCattleBreedOrganism: AnimalBreeds_SwissBlackPiedCattleBreedOrganism,
  SychevkaCattleBreedOrganism: AnimalBreeds_SychevkaCattleBreedOrganism,
  SykiaCattleBreedOrganism: AnimalBreeds_SykiaCattleBreedOrganism,
  MinhotaCattleBreedOrganism: AnimalBreeds_MinhotaCattleBreedOrganism,
  MinorcanCattleBreedOrganism: AnimalBreeds_MinorcanCattleBreedOrganism,
  MishimaCattleBreedOrganism: AnimalBreeds_MishimaCattleBreedOrganism,
  ModeneseCattleBreedOrganism: AnimalBreeds_ModeneseCattleBreedOrganism,
  MonchinaCattleBreedOrganism: AnimalBreeds_MonchinaCattleBreedOrganism,
  MontafonCattleBreedOrganism: AnimalBreeds_MontafonCattleBreedOrganism,
  MontbeliardCattleBreedOrganism: AnimalBreeds_MontbeliardCattleBreedOrganism,
  MorenasDelNoroesteCattleBreedOrganism: AnimalBreeds_MorenasDelNoroesteCattleBreedOrganism,
  MurcianCattleBreedOrganism: AnimalBreeds_MurcianCattleBreedOrganism,
  MurnauWerdenfelsCattleBreedOrganism: AnimalBreeds_MurnauWerdenfelsCattleBreedOrganism,
  TagilCattleBreedOrganism: AnimalBreeds_TagilCattleBreedOrganism,
  TajmaCattleBreedOrganism: AnimalBreeds_TajmaCattleBreedOrganism,
  TambovRedCattleBreedOrganism: AnimalBreeds_TambovRedCattleBreedOrganism,
  TarinaCattleBreedOrganism: AnimalBreeds_TarinaCattleBreedOrganism,
  ThessalyCattleBreedOrganism: AnimalBreeds_ThessalyCattleBreedOrganism,
  TinimaCattleBreedOrganism: AnimalBreeds_TinimaCattleBreedOrganism,
  NantaisCattleBreedOrganism: AnimalBreeds_NantaisCattleBreedOrganism,
  NejdiCattleBreedOrganism: AnimalBreeds_NejdiCattleBreedOrganism,
  NQuoteGabouCattleBreedOrganism: AnimalBreeds_NQuoteGabouCattleBreedOrganism,
  NorthFinncattleCattleBreedOrganism: AnimalBreeds_NorthFinncattleCattleBreedOrganism,
  OropaCattleBreedOrganism: AnimalBreeds_OropaCattleBreedOrganism,
  OulmesBlondCattleBreedOrganism: AnimalBreeds_OulmesBlondCattleBreedOrganism,
  PajunaCattleBreedOrganism: AnimalBreeds_PajunaCattleBreedOrganism,
  PalmeraCattleBreedOrganism: AnimalBreeds_PalmeraCattleBreedOrganism,
  PankotaRedCattleBreedOrganism: AnimalBreeds_PankotaRedCattleBreedOrganism,
  PaphosCattleBreedOrganism: AnimalBreeds_PaphosCattleBreedOrganism,
  TinosCattleBreedOrganism: AnimalBreeds_TinosCattleBreedOrganism,
  TransylvanianPinzgauCattleBreedOrganism: AnimalBreeds_TransylvanianPinzgauCattleBreedOrganism,
  TropicalDairyCattleCattleBreedOrganism: AnimalBreeds_TropicalDairyCattleCattleBreedOrganism,
  TropicanaCattleBreedOrganism: AnimalBreeds_TropicanaCattleBreedOrganism,
  TudancaCattleBreedOrganism: AnimalBreeds_TudancaCattleBreedOrganism,
  TurinoCattleBreedOrganism: AnimalBreeds_TurinoCattleBreedOrganism,
  TurkishBrownCattleBreedOrganism: AnimalBreeds_TurkishBrownCattleBreedOrganism,
  TuxZillertalCattleBreedOrganism: AnimalBreeds_TuxZillertalCattleBreedOrganism,
  TyrolGrayCattleBreedOrganism: AnimalBreeds_TyrolGrayCattleBreedOrganism,
  AbondanceCattleBreedOrganism: AnimalBreeds_AbondanceCattleBreedOrganism,
  AlaTauCattleBreedOrganism: AnimalBreeds_AlaTauCattleBreedOrganism,
  AlbanianIllyrianCattleBreedOrganism: AnimalBreeds_AlbanianIllyrianCattleBreedOrganism,
  AlbanianDwarfCattleBreedOrganism: AnimalBreeds_AlbanianDwarfCattleBreedOrganism,
  UkrainianWhiteheadedCattleBreedOrganism: AnimalBreeds_UkrainianWhiteheadedCattleBreedOrganism,
  UralBlackPiedCattleBreedOrganism: AnimalBreeds_UralBlackPiedCattleBreedOrganism,
  ValdresCattleBreedOrganism: AnimalBreeds_ValdresCattleBreedOrganism,
  VaynolCattleBreedOrganism: AnimalBreeds_VaynolCattleBreedOrganism,
  VerinesaCattleBreedOrganism: AnimalBreeds_VerinesaCattleBreedOrganism,
  VianesaCattleBreedOrganism: AnimalBreeds_VianesaCattleBreedOrganism,
  VillardDeLansCattleBreedOrganism: AnimalBreeds_VillardDeLansCattleBreedOrganism,
  VogelsbergCattleBreedOrganism: AnimalBreeds_VogelsbergCattleBreedOrganism,
  PieRougeDesPlainesCattleBreedOrganism: AnimalBreeds_PieRougeDesPlainesCattleBreedOrganism,
  VorderwaldCattleBreedOrganism: AnimalBreeds_VorderwaldCattleBreedOrganism,
  WestAfricanDwarfShorthornCattleBreedOrganism: AnimalBreeds_WestAfricanDwarfShorthornCattleBreedOrganism,
  WestFinnishCattleBreedOrganism: AnimalBreeds_WestFinnishCattleBreedOrganism,
  WestMacedonianCattleBreedOrganism: AnimalBreeds_WestMacedonianCattleBreedOrganism,
  WhitebredShorthornCattleBreedOrganism: AnimalBreeds_WhitebredShorthornCattleBreedOrganism,
  WhiteGallowayCattleBreedOrganism: AnimalBreeds_WhiteGallowayCattleBreedOrganism,
  WhiteWelshCattleBreedOrganism: AnimalBreeds_WhiteWelshCattleBreedOrganism,
  WitrikCattleBreedOrganism: AnimalBreeds_WitrikCattleBreedOrganism,
  YacumentoCattleBreedOrganism: AnimalBreeds_YacumentoCattleBreedOrganism,
  YaroslavlCattleBreedOrganism: AnimalBreeds_YaroslavlCattleBreedOrganism,
  YurinoCattleBreedOrganism: AnimalBreeds_YurinoCattleBreedOrganism,
  AleppoCattleBreedOrganism: AnimalBreeds_AleppoCattleBreedOrganism,
  SchwyzCattleBreedOrganism: AnimalBreeds_SchwyzCattleBreedOrganism,
  BusaCattleBreedOrganism: AnimalBreeds_BusaCattleBreedOrganism,
  ChiangusCattleBreedOrganism: AnimalBreeds_ChiangusCattleBreedOrganism,
  HallingdalCattleBreedOrganism: AnimalBreeds_HallingdalCattleBreedOrganism,
  DanishJerseyCattleBreedOrganism: AnimalBreeds_DanishJerseyCattleBreedOrganism,
  EnderbyIslandCattleBreedOrganism: AnimalBreeds_EnderbyIslandCattleBreedOrganism,
  GermanAngusCattleBreedOrganism: AnimalBreeds_GermanAngusCattleBreedOrganism,
  IsraeliRedCattleBreedOrganism: AnimalBreeds_IsraeliRedCattleBreedOrganism,
  LinebackCattleBreedOrganism: AnimalBreeds_LinebackCattleBreedOrganism,
  MertolengaCattleBreedOrganism: AnimalBreeds_MertolengaCattleBreedOrganism,
  RedFriesianCattleBreedOrganism: AnimalBreeds_RedFriesianCattleBreedOrganism,
  SenegusCattleBreedOrganism: AnimalBreeds_SenegusCattleBreedOrganism,
  SouthernCriouloCattleBreedOrganism: AnimalBreeds_SouthernCriouloCattleBreedOrganism,
  VosgesCattleBreedOrganism: AnimalBreeds_VosgesCattleBreedOrganism,
  MontanaraCattleBreedOrganism: AnimalBreeds_MontanaraCattleBreedOrganism,
  AlmanzorenaCattleBreedOrganism: AnimalBreeds_AlmanzorenaCattleBreedOrganism,
  LorquinaCattleBreedOrganism: AnimalBreeds_LorquinaCattleBreedOrganism,
  CalasparrenaCattleBreedOrganism: AnimalBreeds_CalasparrenaCattleBreedOrganism,
  AmritmahalCattleBreedOrganism: AnimalBreeds_AmritmahalCattleBreedOrganism,
  BachaurCattleBreedOrganism: AnimalBreeds_BachaurCattleBreedOrganism,
  BarkaCattleBreedOrganism: AnimalBreeds_BarkaCattleBreedOrganism,
  BengaliCattleBreedOrganism: AnimalBreeds_BengaliCattleBreedOrganism,
  BhagnariCattleBreedOrganism: AnimalBreeds_BhagnariCattleBreedOrganism,
  BoranCattleBreedOrganism: AnimalBreeds_BoranCattleBreedOrganism,
  ChanniCattleBreedOrganism: AnimalBreeds_ChanniCattleBreedOrganism,
  CholistaniCattleBreedOrganism: AnimalBreeds_CholistaniCattleBreedOrganism,
  DajalCattleBreedOrganism: AnimalBreeds_DajalCattleBreedOrganism,
  DangiCattleBreedOrganism: AnimalBreeds_DangiCattleBreedOrganism,
  DeoniCattleBreedOrganism: AnimalBreeds_DeoniCattleBreedOrganism,
  DhanniCattleBreedOrganism: AnimalBreeds_DhanniCattleBreedOrganism,
  GaolaoCattleBreedOrganism: AnimalBreeds_GaolaoCattleBreedOrganism,
  HallikarCattleBreedOrganism: AnimalBreeds_HallikarCattleBreedOrganism,
  HarianaCattleBreedOrganism: AnimalBreeds_HarianaCattleBreedOrganism,
  IndoBrazilianCattleBreedOrganism: AnimalBreeds_IndoBrazilianCattleBreedOrganism,
  KangayamCattleBreedOrganism: AnimalBreeds_KangayamCattleBreedOrganism,
  KankrejCattleBreedOrganism: AnimalBreeds_KankrejCattleBreedOrganism,
  KenkathaCattleBreedOrganism: AnimalBreeds_KenkathaCattleBreedOrganism,
  KherigarhCattleBreedOrganism: AnimalBreeds_KherigarhCattleBreedOrganism,
  KhillariCattleBreedOrganism: AnimalBreeds_KhillariCattleBreedOrganism,
  KrishnaValleyCattleBreedOrganism: AnimalBreeds_KrishnaValleyCattleBreedOrganism,
  LohaniCattleBreedOrganism: AnimalBreeds_LohaniCattleBreedOrganism,
  MalviCattleBreedOrganism: AnimalBreeds_MalviCattleBreedOrganism,
  MewatiCattleBreedOrganism: AnimalBreeds_MewatiCattleBreedOrganism,
  NagoriCattleBreedOrganism: AnimalBreeds_NagoriCattleBreedOrganism,
  NeloreCattleBreedOrganism: AnimalBreeds_NeloreCattleBreedOrganism,
  NimariCattleBreedOrganism: AnimalBreeds_NimariCattleBreedOrganism,
  PonwarCattleBreedOrganism: AnimalBreeds_PonwarCattleBreedOrganism,
  RathCattleBreedOrganism: AnimalBreeds_RathCattleBreedOrganism,
  RathiCattleBreedOrganism: AnimalBreeds_RathiCattleBreedOrganism,
  RedSindhiCattleBreedOrganism: AnimalBreeds_RedSindhiCattleBreedOrganism,
  RojhanCattleBreedOrganism: AnimalBreeds_RojhanCattleBreedOrganism,
  SahiwalCattleBreedOrganism: AnimalBreeds_SahiwalCattleBreedOrganism,
  SiriZebuCattleBreedOrganism: AnimalBreeds_SiriZebuCattleBreedOrganism,
  TharparkarCattleBreedOrganism: AnimalBreeds_TharparkarCattleBreedOrganism,
  ZanzibarZebuCattleBreedOrganism: AnimalBreeds_ZanzibarZebuCattleBreedOrganism,
  ArsiCattleBreedOrganism: AnimalBreeds_ArsiCattleBreedOrganism,
  AtpadiMahalCattleBreedOrganism: AnimalBreeds_AtpadiMahalCattleBreedOrganism,
  AzaouakCattleBreedOrganism: AnimalBreeds_AzaouakCattleBreedOrganism,
  AzerbaijanZebuCattleBreedOrganism: AnimalBreeds_AzerbaijanZebuCattleBreedOrganism,
  BaggaraCattleBreedOrganism: AnimalBreeds_BaggaraCattleBreedOrganism,
  BambawaCattleBreedOrganism: AnimalBreeds_BambawaCattleBreedOrganism,
  BamiCattleBreedOrganism: AnimalBreeds_BamiCattleBreedOrganism,
  BanyoCattleBreedOrganism: AnimalBreeds_BanyoCattleBreedOrganism,
  BargurCattleBreedOrganism: AnimalBreeds_BargurCattleBreedOrganism,
  BariCattleBreedOrganism: AnimalBreeds_BariCattleBreedOrganism,
  BimalCattleBreedOrganism: AnimalBreeds_BimalCattleBreedOrganism,
  BorneoZebuCattleBreedOrganism: AnimalBreeds_BorneoZebuCattleBreedOrganism,
  ButanaCattleBreedOrganism: AnimalBreeds_ButanaCattleBreedOrganism,
  ChittagongRedCattleBreedOrganism: AnimalBreeds_ChittagongRedCattleBreedOrganism,
  CutchiCattleBreedOrganism: AnimalBreeds_CutchiCattleBreedOrganism,
  DairyZebuOfUberabaCattleBreedOrganism: AnimalBreeds_DairyZebuOfUberabaCattleBreedOrganism,
  DashtiariCattleBreedOrganism: AnimalBreeds_DashtiariCattleBreedOrganism,
  DialiCattleBreedOrganism: AnimalBreeds_DialiCattleBreedOrganism,
  DidingaCattleBreedOrganism: AnimalBreeds_DidingaCattleBreedOrganism,
  DongolaCattleBreedOrganism: AnimalBreeds_DongolaCattleBreedOrganism,
  FellataCattleBreedOrganism: AnimalBreeds_FellataCattleBreedOrganism,
  TurkmenZebuCattleBreedOrganism: AnimalBreeds_TurkmenZebuCattleBreedOrganism,
  AbyssinianHighlandZebuCattleBreedOrganism: AnimalBreeds_AbyssinianHighlandZebuCattleBreedOrganism,
  AbyssinianShorthornedZebuCattleBreedOrganism: AnimalBreeds_AbyssinianShorthornedZebuCattleBreedOrganism,
  AcehCattleBreedOrganism: AnimalBreeds_AcehCattleBreedOrganism,
  AchhamCattleBreedOrganism: AnimalBreeds_AchhamCattleBreedOrganism,
  GarreCattleBreedOrganism: AnimalBreeds_GarreCattleBreedOrganism,
  GasaraCattleBreedOrganism: AnimalBreeds_GasaraCattleBreedOrganism,
  GobraCattleBreedOrganism: AnimalBreeds_GobraCattleBreedOrganism,
  GoomsurCattleBreedOrganism: AnimalBreeds_GoomsurCattleBreedOrganism,
  GujamavuCattleBreedOrganism: AnimalBreeds_GujamavuCattleBreedOrganism,
  LeiqiongCattleBreedOrganism: AnimalBreeds_LeiqiongCattleBreedOrganism,
  HissarCattleBreedOrganism: AnimalBreeds_HissarCattleBreedOrganism,
  IngessanaCattleBreedOrganism: AnimalBreeds_IngessanaCattleBreedOrganism,
  JamaicaBrahmanCattleBreedOrganism: AnimalBreeds_JamaicaBrahmanCattleBreedOrganism,
  JellicutCattleBreedOrganism: AnimalBreeds_JellicutCattleBreedOrganism,
  AdamawaCattleBreedOrganism: AnimalBreeds_AdamawaCattleBreedOrganism,
  AdenZebuCattleBreedOrganism: AnimalBreeds_AdenZebuCattleBreedOrganism,
  AfghanCattleBreedOrganism: AnimalBreeds_AfghanCattleBreedOrganism,
  AlambadiCattleBreedOrganism: AnimalBreeds_AlambadiCattleBreedOrganism,
  UmblacheryCattleBreedOrganism: AnimalBreeds_UmblacheryCattleBreedOrganism,
  VenezuelanZebuCattleBreedOrganism: AnimalBreeds_VenezuelanZebuCattleBreedOrganism,
  PantaneiroCattleBreedOrganism: AnimalBreeds_PantaneiroCattleBreedOrganism,
  JenubiCattleBreedOrganism: AnimalBreeds_JenubiCattleBreedOrganism,
  JidduCattleBreedOrganism: AnimalBreeds_JidduCattleBreedOrganism,
  JijjigaZebuCattleBreedOrganism: AnimalBreeds_JijjigaZebuCattleBreedOrganism,
  KabotaCattleBreedOrganism: AnimalBreeds_KabotaCattleBreedOrganism,
  KachchaSiriCattleBreedOrganism: AnimalBreeds_KachchaSiriCattleBreedOrganism,
  KalakheriCattleBreedOrganism: AnimalBreeds_KalakheriCattleBreedOrganism,
  KamdhinoCattleBreedOrganism: AnimalBreeds_KamdhinoCattleBreedOrganism,
  KandahariCattleBreedOrganism: AnimalBreeds_KandahariCattleBreedOrganism,
  KaninganCattleBreedOrganism: AnimalBreeds_KaninganCattleBreedOrganism,
  WakwaCattleBreedOrganism: AnimalBreeds_WakwaCattleBreedOrganism,
  WhiteFulaniCattleBreedOrganism: AnimalBreeds_WhiteFulaniCattleBreedOrganism,
  YemeniZebuCattleBreedOrganism: AnimalBreeds_YemeniZebuCattleBreedOrganism,
  IranianZebuCattleBreedOrganism: AnimalBreeds_IranianZebuCattleBreedOrganism,
  KhorsanCattleBreedOrganism: AnimalBreeds_KhorsanCattleBreedOrganism,
  PolledGirCattleBreedOrganism: AnimalBreeds_PolledGirCattleBreedOrganism,
  KappiliyanCattleBreedOrganism: AnimalBreeds_KappiliyanCattleBreedOrganism,
  KaramajongCattleBreedOrganism: AnimalBreeds_KaramajongCattleBreedOrganism,
  KenanaCattleBreedOrganism: AnimalBreeds_KenanaCattleBreedOrganism,
  KenyaBoranCattleBreedOrganism: AnimalBreeds_KenyaBoranCattleBreedOrganism,
  KenyaZebuCattleBreedOrganism: AnimalBreeds_KenyaZebuCattleBreedOrganism,
  KhamalaCattleBreedOrganism: AnimalBreeds_KhamalaCattleBreedOrganism,
  KhurasaniZebuCattleBreedOrganism: AnimalBreeds_KhurasaniZebuCattleBreedOrganism,
  KilaraCattleBreedOrganism: AnimalBreeds_KilaraCattleBreedOrganism,
  KinniyaCattleBreedOrganism: AnimalBreeds_KinniyaCattleBreedOrganism,
  KonariCattleBreedOrganism: AnimalBreeds_KonariCattleBreedOrganism,
  GuzeratCattleBreedOrganism: AnimalBreeds_GuzeratCattleBreedOrganism,
  TadzhikZebuCattleBreedOrganism: AnimalBreeds_TadzhikZebuCattleBreedOrganism,
  DeogirCattleBreedOrganism: AnimalBreeds_DeogirCattleBreedOrganism,
  GayalCattleBreedOrganism: AnimalBreeds_GayalCattleBreedOrganism,
  AmericanBisonXCattleBreedOrganism: AnimalBreeds_AmericanBisonXCattleBreedOrganism,
  AustralianBrafordCattleBreedOrganism: AnimalBreeds_AustralianBrafordCattleBreedOrganism,
  KrishnagariCattleBreedOrganism: AnimalBreeds_KrishnagariCattleBreedOrganism,
  KumauniCattleBreedOrganism: AnimalBreeds_KumauniCattleBreedOrganism,
  LadakhiCattleBreedOrganism: AnimalBreeds_LadakhiCattleBreedOrganism,
  LatukaCattleBreedOrganism: AnimalBreeds_LatukaCattleBreedOrganism,
  LugwareCattleBreedOrganism: AnimalBreeds_LugwareCattleBreedOrganism,
  MadagascarZebuCattleBreedOrganism: AnimalBreeds_MadagascarZebuCattleBreedOrganism,
  MadaripurCattleBreedOrganism: AnimalBreeds_MadaripurCattleBreedOrganism,
  MagalCattleBreedOrganism: AnimalBreeds_MagalCattleBreedOrganism,
  MalawiZebuCattleBreedOrganism: AnimalBreeds_MalawiZebuCattleBreedOrganism,
  MalnadGiddaCattleBreedOrganism: AnimalBreeds_MalnadGiddaCattleBreedOrganism,
  AustralianFriesianSahiwalCattleBreedOrganism: AnimalBreeds_AustralianFriesianSahiwalCattleBreedOrganism,
  BrafordCattleBreedOrganism: AnimalBreeds_BrafordCattleBreedOrganism,
  BrahmousinCattleBreedOrganism: AnimalBreeds_BrahmousinCattleBreedOrganism,
  CanchimCattleBreedOrganism: AnimalBreeds_CanchimCattleBreedOrganism,
  CharbrayCattleBreedOrganism: AnimalBreeds_CharbrayCattleBreedOrganism,
  DroughtmasterCattleBreedOrganism: AnimalBreeds_DroughtmasterCattleBreedOrganism,
  MampatiCattleBreedOrganism: AnimalBreeds_MampatiCattleBreedOrganism,
  ManapariCattleBreedOrganism: AnimalBreeds_ManapariCattleBreedOrganism,
  MaureCattleBreedOrganism: AnimalBreeds_MaureCattleBreedOrganism,
  MazandaraniCattleBreedOrganism: AnimalBreeds_MazandaraniCattleBreedOrganism,
  MeraukeCattleBreedOrganism: AnimalBreeds_MeraukeCattleBreedOrganism,
  MhaswadCattleBreedOrganism: AnimalBreeds_MhaswadCattleBreedOrganism,
  MiniatureZebuCattleBreedOrganism: AnimalBreeds_MiniatureZebuCattleBreedOrganism,
  MongallaCattleBreedOrganism: AnimalBreeds_MongallaCattleBreedOrganism,
  MorangCattleBreedOrganism: AnimalBreeds_MorangCattleBreedOrganism,
  GelbrayCattleBreedOrganism: AnimalBreeds_GelbrayCattleBreedOrganism,
  JamaicaBlackCattleBreedOrganism: AnimalBreeds_JamaicaBlackCattleBreedOrganism,
  JamaicaHopeCattleBreedOrganism: AnimalBreeds_JamaicaHopeCattleBreedOrganism,
  JamaicaRedCattleBreedOrganism: AnimalBreeds_JamaicaRedCattleBreedOrganism,
  KaranFriesCattleBreedOrganism: AnimalBreeds_KaranFriesCattleBreedOrganism,
  KaranSwissCattleBreedOrganism: AnimalBreeds_KaranSwissCattleBreedOrganism,
  MozambiqueAngoniCattleBreedOrganism: AnimalBreeds_MozambiqueAngoniCattleBreedOrganism,
  MpwapwaCattleBreedOrganism: AnimalBreeds_MpwapwaCattleBreedOrganism,
  MurleCattleBreedOrganism: AnimalBreeds_MurleCattleBreedOrganism,
  NakaliCattleBreedOrganism: AnimalBreeds_NakaliCattleBreedOrganism,
  NepaleseHillZebuCattleBreedOrganism: AnimalBreeds_NepaleseHillZebuCattleBreedOrganism,
  NQuoteGaoundereCattleBreedOrganism: AnimalBreeds_NQuoteGaoundereCattleBreedOrganism,
  NkediCattleBreedOrganism: AnimalBreeds_NkediCattleBreedOrganism,
  NorthBangladeshGrayCattleBreedOrganism: AnimalBreeds_NorthBangladeshGrayCattleBreedOrganism,
  NorthSomaliZebuCattleBreedOrganism: AnimalBreeds_NorthSomaliZebuCattleBreedOrganism,
  PolledGuzeratCattleBreedOrganism: AnimalBreeds_PolledGuzeratCattleBreedOrganism,
  MandalongCattleBreedOrganism: AnimalBreeds_MandalongCattleBreedOrganism,
  AustralianMilkingZebuCattleBreedOrganism: AnimalBreeds_AustralianMilkingZebuCattleBreedOrganism,
  RedBrangusCattleBreedOrganism: AnimalBreeds_RedBrangusCattleBreedOrganism,
  SantaCruzCattleBreedOrganism: AnimalBreeds_SantaCruzCattleBreedOrganism,
  SiboneyCattleBreedOrganism: AnimalBreeds_SiboneyCattleBreedOrganism,
  BambaraCattleBreedOrganism: AnimalBreeds_BambaraCattleBreedOrganism,
  PolledNeloreCattleBreedOrganism: AnimalBreeds_PolledNeloreCattleBreedOrganism,
  PrewakwaCattleBreedOrganism: AnimalBreeds_PrewakwaCattleBreedOrganism,
  PulMQuoteBorCattleBreedOrganism: AnimalBreeds_PulMQuoteBorCattleBreedOrganism,
  PunganurCattleBreedOrganism: AnimalBreeds_PunganurCattleBreedOrganism,
  RamgarhiCattleBreedOrganism: AnimalBreeds_RamgarhiCattleBreedOrganism,
  RedBororoCattleBreedOrganism: AnimalBreeds_RedBororoCattleBreedOrganism,
  RedDesertCattleBreedOrganism: AnimalBreeds_RedDesertCattleBreedOrganism,
  RedKandhariCattleBreedOrganism: AnimalBreeds_RedKandhariCattleBreedOrganism,
  ShakhansurriCattleBreedOrganism: AnimalBreeds_ShakhansurriCattleBreedOrganism,
  ShekoCattleBreedOrganism: AnimalBreeds_ShekoCattleBreedOrganism,
  BambeyCattleBreedOrganism: AnimalBreeds_BambeyCattleBreedOrganism,
  BatanesBlackCattleBreedOrganism: AnimalBreeds_BatanesBlackCattleBreedOrganism,
  BorgouCattleBreedOrganism: AnimalBreeds_BorgouCattleBreedOrganism,
  BrahornCattleBreedOrganism: AnimalBreeds_BrahornCattleBreedOrganism,
  BralersCattleBreedOrganism: AnimalBreeds_BralersCattleBreedOrganism,
  BraMaineCattleBreedOrganism: AnimalBreeds_BraMaineCattleBreedOrganism,
  ShendiCattleBreedOrganism: AnimalBreeds_ShendiCattleBreedOrganism,
  ShuwaCattleBreedOrganism: AnimalBreeds_ShuwaCattleBreedOrganism,
  SinhalaCattleBreedOrganism: AnimalBreeds_SinhalaCattleBreedOrganism,
  SistaniCattleBreedOrganism: AnimalBreeds_SistaniCattleBreedOrganism,
  SmallEastAfricanZebuCattleBreedOrganism: AnimalBreeds_SmallEastAfricanZebuCattleBreedOrganism,
  SokotoGudaliCattleBreedOrganism: AnimalBreeds_SokotoGudaliCattleBreedOrganism,
  SomaliCattleBreedOrganism: AnimalBreeds_SomaliCattleBreedOrganism,
  SonkheriCattleBreedOrganism: AnimalBreeds_SonkheriCattleBreedOrganism,
  SonValleyCattleBreedOrganism: AnimalBreeds_SonValleyCattleBreedOrganism,
  SouthChinaZebuCattleBreedOrganism: AnimalBreeds_SouthChinaZebuCattleBreedOrganism,
  BraSwissCattleBreedOrganism: AnimalBreeds_BraSwissCattleBreedOrganism,
  BravonCattleBreedOrganism: AnimalBreeds_BravonCattleBreedOrganism,
  BrazilianDairyHybridCattleOrganism: AnimalBreeds_BrazilianDairyHybridCattleOrganism,
  BurmeseCattleBreedOrganism: AnimalBreeds_BurmeseCattleBreedOrganism,
  BushuevCattleBreedOrganism: AnimalBreeds_BushuevCattleBreedOrganism,
  CaiuaCattleBreedOrganism: AnimalBreeds_CaiuaCattleBreedOrganism,
  SouthMalawiZebuCattleBreedOrganism: AnimalBreeds_SouthMalawiZebuCattleBreedOrganism,
  SudaneseFulaniCattleBreedOrganism: AnimalBreeds_SudaneseFulaniCattleBreedOrganism,
  TabapuaCattleBreedOrganism: AnimalBreeds_TabapuaCattleBreedOrganism,
  TamankaduwaCattleBreedOrganism: AnimalBreeds_TamankaduwaCattleBreedOrganism,
  TanzanianZebuCattleBreedOrganism: AnimalBreeds_TanzanianZebuCattleBreedOrganism,
  TaraiCattleBreedOrganism: AnimalBreeds_TaraiCattleBreedOrganism,
  ThillariCattleBreedOrganism: AnimalBreeds_ThillariCattleBreedOrganism,
  ToposaCattleBreedOrganism: AnimalBreeds_ToposaCattleBreedOrganism,
  ToronkeCattleBreedOrganism: AnimalBreeds_ToronkeCattleBreedOrganism,
  ToupouriCattleBreedOrganism: AnimalBreeds_ToupouriCattleBreedOrganism,
  CarazebuCattleBreedOrganism: AnimalBreeds_CarazebuCattleBreedOrganism,
  CentralAsianZebuCattleBreedOrganism: AnimalBreeds_CentralAsianZebuCattleBreedOrganism,
  CharfordCattleBreedOrganism: AnimalBreeds_CharfordCattleBreedOrganism,
  CubanCriolloCattleBreedOrganism: AnimalBreeds_CubanCriolloCattleBreedOrganism,
  CubanZebuCattleBreedOrganism: AnimalBreeds_CubanZebuCattleBreedOrganism,
  DishtyCattleBreedOrganism: AnimalBreeds_DishtyCattleBreedOrganism,
  DjakoreCattleBreedOrganism: AnimalBreeds_DjakoreCattleBreedOrganism,
  GambianNQuoteDamaCattleBreedOrganism: AnimalBreeds_GambianNQuoteDamaCattleBreedOrganism,
  GhanaSangaCattleBreedOrganism: AnimalBreeds_GhanaSangaCattleBreedOrganism,
  GirolandoCattleBreedOrganism: AnimalBreeds_GirolandoCattleBreedOrganism,
  GuzerandoCattleBreedOrganism: AnimalBreeds_GuzerandoCattleBreedOrganism,
  HattonCattleBreedOrganism: AnimalBreeds_HattonCattleBreedOrganism,
  IbageCattleBreedOrganism: AnimalBreeds_IbageCattleBreedOrganism,
  IraqiCattleBreedOrganism: AnimalBreeds_IraqiCattleBreedOrganism,
  JerdiCattleBreedOrganism: AnimalBreeds_JerdiCattleBreedOrganism,
  JersindCattleBreedOrganism: AnimalBreeds_JersindCattleBreedOrganism,
  JotkoCattleBreedOrganism: AnimalBreeds_JotkoCattleBreedOrganism,
  KanemCattleBreedOrganism: AnimalBreeds_KanemCattleBreedOrganism,
  KetekuCattleBreedOrganism: AnimalBreeds_KetekuCattleBreedOrganism,
  LaviniaCattleBreedOrganism: AnimalBreeds_LaviniaCattleBreedOrganism,
  LocalIndianDairyCattleBreedOrganism: AnimalBreeds_LocalIndianDairyCattleBreedOrganism,
  MantiqueiraCattleBreedOrganism: AnimalBreeds_MantiqueiraCattleBreedOrganism,
  NdaguCattleBreedOrganism: AnimalBreeds_NdaguCattleBreedOrganism,
  NormanzuCattleBreedOrganism: AnimalBreeds_NormanzuCattleBreedOrganism,
  NubaMountainCattleBreedOrganism: AnimalBreeds_NubaMountainCattleBreedOrganism,
  PabnaCattleBreedOrganism: AnimalBreeds_PabnaCattleBreedOrganism,
  MixedPerijaneroCattleBreedOrganism: AnimalBreeds_MixedPerijaneroCattleBreedOrganism,
  PitangueirasCattleBreedOrganism: AnimalBreeds_PitangueirasCattleBreedOrganism,
  QuasahCattleBreedOrganism: AnimalBreeds_QuasahCattleBreedOrganism,
  RanaCattleOrganism: AnimalBreeds_RanaCattleOrganism,
  RangerCattleOrganism: AnimalBreeds_RangerCattleOrganism,
  ReniteloCattleBreedOrganism: AnimalBreeds_ReniteloCattleBreedOrganism,
  RiopardenzeCattleBreedOrganism: AnimalBreeds_RiopardenzeCattleBreedOrganism,
  RustaqiCattleBreedOrganism: AnimalBreeds_RustaqiCattleBreedOrganism,
  SabreCattleBreedOrganism: AnimalBreeds_SabreCattleBreedOrganism,
  SahfordCattleBreedOrganism: AnimalBreeds_SahfordCattleBreedOrganism,
  SchwyzZeboidCattleBreedOrganism: AnimalBreeds_SchwyzZeboidCattleBreedOrganism,
  SuiaCattleBreedOrganism: AnimalBreeds_SuiaCattleBreedOrganism,
  SuisbuCattleBreedOrganism: AnimalBreeds_SuisbuCattleBreedOrganism,
  SunandiniCattleBreedOrganism: AnimalBreeds_SunandiniCattleBreedOrganism,
  TainoCattleBreedOrganism: AnimalBreeds_TainoCattleBreedOrganism,
  ThibarCattleBreedOrganism: AnimalBreeds_ThibarCattleBreedOrganism,
  ToubouCattleBreedOrganism: AnimalBreeds_ToubouCattleBreedOrganism,
  TropicalCattleBreedOrganism: AnimalBreeds_TropicalCattleBreedOrganism,
  TSSH1CattleBreedOrganism: AnimalBreeds_TSSH1CattleBreedOrganism,
  VictoriaCattleBreedOrganism: AnimalBreeds_VictoriaCattleBreedOrganism,
  WokalupCattleBreedOrganism: AnimalBreeds_WokalupCattleBreedOrganism,
  MaduraCattleBreedOrganism: AnimalBreeds_MaduraCattleBreedOrganism,
  ShireHorseOrganism: AnimalBreeds_ShireHorseOrganism,
  BeltedGallowayCattleBreedOrganism: AnimalBreeds_BeltedGallowayCattleBreedOrganism,
  PanamaSheepOrganism: AnimalBreeds_PanamaSheepOrganism,
  FHCElitePig9Organism: AnimalBreeds_FHCElitePig9Organism,
  YucaPigOrganism: AnimalBreeds_YucaPigOrganism,
  BritishSaddlebackPigOrganism: AnimalBreeds_BritishSaddlebackPigOrganism,
  AngloNubianGoatOrganism: AnimalBreeds_AngloNubianGoatOrganism,
  BeefmasterCattleBreedOrganism: AnimalBreeds_BeefmasterCattleBreedOrganism,
  PicCambourghPigOrganism: AnimalBreeds_PicCambourghPigOrganism,
  TrakehnerHorseOrganism: AnimalBreeds_TrakehnerHorseOrganism,
  BoarPowerPig72Organism: AnimalBreeds_BoarPowerPig72Organism,
  OrpingtonChickenOrganism: AnimalBreeds_OrpingtonChickenOrganism,
  HunterHorseOrganism: AnimalBreeds_HunterHorseOrganism,
  HerefordPigOrganism: AnimalBreeds_HerefordPigOrganism,
  PietrainPigOrganism: AnimalBreeds_PietrainPigOrganism,
  HampshirePigOrganism: AnimalBreeds_HampshirePigOrganism,
  WhiteTurkeyOrganism: AnimalBreeds_WhiteTurkeyOrganism,
  MeleagrisGallopavoOrganism: AnimalBreeds_MeleagrisGallopavoOrganism,
  LargeBlackPigOrganism: AnimalBreeds_LargeBlackPigOrganism,
  SaanenGoatOrganism: AnimalBreeds_SaanenGoatOrganism,
  RambouilletSheepOrganism: AnimalBreeds_RambouilletSheepOrganism,
  MorganHorseOrganism: AnimalBreeds_MorganHorseOrganism,
  LuingCattleBreedOrganism: AnimalBreeds_LuingCattleBreedOrganism,
  CanadianCattleBreedOrganism: AnimalBreeds_CanadianCattleBreedOrganism,
  CommercialWhiteEggLayerTypeChickenOrganism: AnimalBreeds_CommercialWhiteEggLayerTypeChickenOrganism,
  BritishLopPigOrganism: AnimalBreeds_BritishLopPigOrganism,
  MaransChickenOrganism: AnimalBreeds_MaransChickenOrganism,
  BelgianHorseOrganism: AnimalBreeds_BelgianHorseOrganism,
  JapaneseChickenOrganism: AnimalBreeds_JapaneseChickenOrganism,
  DomesticGooseOrganism: AnimalBreeds_DomesticGooseOrganism,
  HaysConverterCattleBreedOrganism: AnimalBreeds_HaysConverterCattleBreedOrganism,
  BlackWingedBronzeTurkeyOrganism: AnimalBreeds_BlackWingedBronzeTurkeyOrganism,
  FHCElitePig3Organism: AnimalBreeds_FHCElitePig3Organism,
  BoarPowerPig414Organism: AnimalBreeds_BoarPowerPig414Organism,
  PekinDuckOrganism: AnimalBreeds_PekinDuckOrganism,
  CPFPigOrganism: AnimalBreeds_CPFPigOrganism,
  PennaPalmTurkeyOrganism: AnimalBreeds_PennaPalmTurkeyOrganism,
  DorsetSheepSuperbreedOrganism: AnimalBreeds_DorsetSheepSuperbreedOrganism,
  WestphalianHorseOrganism: AnimalBreeds_WestphalianHorseOrganism,
  BarbadosSheepOrganism: AnimalBreeds_BarbadosSheepOrganism,
  HolsteinerHorseOrganism: AnimalBreeds_HolsteinerHorseOrganism,
  SmallWhiteTurkeyOrganism: AnimalBreeds_SmallWhiteTurkeyOrganism,
  BoarPowerPig929Organism: AnimalBreeds_BoarPowerPig929Organism,
  HolsteinFriesianCattleBreedOrganism: AnimalBreeds_HolsteinFriesianCattleBreedOrganism,
  WhiteParkCattleBreedOrganism: AnimalBreeds_WhiteParkCattleBreedOrganism,
  NewForestPonyOrganism: AnimalBreeds_NewForestPonyOrganism,
  AmericanCreamHorseOrganism: AnimalBreeds_AmericanCreamHorseOrganism,
  AmericanTrotterHorseOrganism: AnimalBreeds_AmericanTrotterHorseOrganism,
  SlateTurkeyOrganism: AnimalBreeds_SlateTurkeyOrganism,
  BrangusCattleBreedOrganism: AnimalBreeds_BrangusCattleBreedOrganism,
  SwissAlpineGoatOrganism: AnimalBreeds_SwissAlpineGoatOrganism,
  SimmentalCattleBreedOrganism: AnimalBreeds_SimmentalCattleBreedOrganism,
  BlackWelshCattleBreedOrganism: AnimalBreeds_BlackWelshCattleBreedOrganism,
  BoarPowerPig565Organism: AnimalBreeds_BoarPowerPig565Organism,
  PicLinePig26Organism: AnimalBreeds_PicLinePig26Organism,
  FaverolleChickenOrganism: AnimalBreeds_FaverolleChickenOrganism,
  LaFlecheChickenOrganism: AnimalBreeds_LaFlecheChickenOrganism,
  ConnorPrairiePigOrganism: AnimalBreeds_ConnorPrairiePigOrganism,
  ToggenburgGoatOrganism: AnimalBreeds_ToggenburgGoatOrganism,
  BelgianBeardedChickenOrganism: AnimalBreeds_BelgianBeardedChickenOrganism,
  BrahmaCattleBreedOrganism: AnimalBreeds_BrahmaCattleBreedOrganism,
  BroadBreastedBronzeTurkeyOrganism: AnimalBreeds_BroadBreastedBronzeTurkeyOrganism,
  LacombePigOrganism: AnimalBreeds_LacombePigOrganism,
  TamworthPigOrganism: AnimalBreeds_TamworthPigOrganism,
  BoarPowerPig84Organism: AnimalBreeds_BoarPowerPig84Organism,
  RomneyMarshSheepOrganism: AnimalBreeds_RomneyMarshSheepOrganism,
  YorkshireCoachHorseOrganism: AnimalBreeds_YorkshireCoachHorseOrganism,
  RomanovSheepOrganism: AnimalBreeds_RomanovSheepOrganism,
  LincolnRedCattleBreedOrganism: AnimalBreeds_LincolnRedCattleBreedOrganism,
  BoarPowerPig48Organism: AnimalBreeds_BoarPowerPig48Organism,
  BlackTurkeyOrganism: AnimalBreeds_BlackTurkeyOrganism,
  CommercialMeatTypeChickenOrganism: AnimalBreeds_CommercialMeatTypeChickenOrganism,
  DurocPigOrganism: AnimalBreeds_DurocPigOrganism,
  FHCElitePig7Organism: AnimalBreeds_FHCElitePig7Organism,
  GirCattleBreedOrganism: AnimalBreeds_GirCattleBreedOrganism,
  BoarPowerPig616Organism: AnimalBreeds_BoarPowerPig616Organism,
  PhoenixChickenOrganism: AnimalBreeds_PhoenixChickenOrganism,
  KentuckyRedBerkshirePigOrganism: AnimalBreeds_KentuckyRedBerkshirePigOrganism,
  ButtercupChickenOrganism: AnimalBreeds_ButtercupChickenOrganism,
  StandardbredHorseOrganism: AnimalBreeds_StandardbredHorseOrganism,
  BoarPowerPig828Organism: AnimalBreeds_BoarPowerPig828Organism,
  BosTaurusOrganism: AnimalBreeds_BosTaurusOrganism,
  ChianinaCattleBreedOrganism: AnimalBreeds_ChianinaCattleBreedOrganism,
  EquusCaballusOrganism: AnimalBreeds_EquusCaballusOrganism,
  SouthdownSheepOrganism: AnimalBreeds_SouthdownSheepOrganism,
  LeghornChickenOrganism: AnimalBreeds_LeghornChickenOrganism,
  DomesticFowlOrganism: AnimalBreeds_DomesticFowlOrganism,
  KleenLeenBlackPigOrganism: AnimalBreeds_KleenLeenBlackPigOrganism,
  SpottedPigOrganism: AnimalBreeds_SpottedPigOrganism,
  DekalbHybridPigLine31Organism: AnimalBreeds_DekalbHybridPigLine31Organism,
  BroadBreastedWhiteTurkeyOrganism: AnimalBreeds_BroadBreastedWhiteTurkeyOrganism,
  BeltsvilleWhiteTurkeyOrganism: AnimalBreeds_BeltsvilleWhiteTurkeyOrganism,
  NormanCoachHorseOrganism: AnimalBreeds_NormanCoachHorseOrganism,
  BuckeyeChickenOrganism: AnimalBreeds_BuckeyeChickenOrganism,
  ArigeoisPonyBreedOrganism: AnimalBreeds_ArigeoisPonyBreedOrganism,
  WiltshireHornSheepOrganism: AnimalBreeds_WiltshireHornSheepOrganism,
  PonyOrganism: AnimalBreeds_PonyOrganism,
  PinzgauerCattleBreedOrganism: AnimalBreeds_PinzgauerCattleBreedOrganism,
  GalwaySheepBreedOrganism: AnimalBreeds_GalwaySheepBreedOrganism,
  UkrainianSteppeWhitePigOrganism: AnimalBreeds_UkrainianSteppeWhitePigOrganism,
  BrabantHorseOrganism: AnimalBreeds_BrabantHorseOrganism,
  GypsyVannerHorseOrganism: AnimalBreeds_GypsyVannerHorseOrganism,
  MurgeseHorseOrganism: AnimalBreeds_MurgeseHorseOrganism,
  MixedBreedHorseOrganism: AnimalBreeds_MixedBreedHorseOrganism,
  MixedBreedSheepOrganism: AnimalBreeds_MixedBreedSheepOrganism,
  MixedBreedChickenOrganism: AnimalBreeds_MixedBreedChickenOrganism,
  WarmbloodHorseBreedOrganism: AnimalBreeds_WarmbloodHorseBreedOrganism,
  BlackAngusCattleBreedOrganism: AnimalBreeds_BlackAngusCattleBreedOrganism,
  MixedBreedCattleOrganism: AnimalBreeds_MixedBreedCattleOrganism,
  MasaiCattleBreedOrganism: AnimalBreeds_MasaiCattleBreedOrganism,
  AntwerpBeardedBantamOrganism: AnimalBreeds_AntwerpBeardedBantamOrganism,
  BantamOrganism: AnimalBreeds_BantamOrganism,
  MoulardDuckBreedOrganism: AnimalBreeds_MoulardDuckBreedOrganism,
  ClevelandBayHorseOrganism: AnimalBreeds_ClevelandBayHorseOrganism,
  PlymouthRockChickenOrganism: AnimalBreeds_PlymouthRockChickenOrganism,
  JungleFowlChickenOrganism: AnimalBreeds_JungleFowlChickenOrganism,
  DekalbHybridPigLine30Organism: AnimalBreeds_DekalbHybridPigLine30Organism,
  MixedBreedPigOrganism: AnimalBreeds_MixedBreedPigOrganism,
  PerendaleSheepOrganism: AnimalBreeds_PerendaleSheepOrganism,
  PalominoHorseOrganism: AnimalBreeds_PalominoHorseOrganism,
  LipizzanerHorseOrganism: AnimalBreeds_LipizzanerHorseOrganism,
  BisonBisonXSimmentalHybridCattleBreedOrganism: AnimalBreeds_BisonBisonXSimmentalHybridCattleBreedOrganism,
  BisonBisonXBosTaurusHybridOrganism: AnimalBreeds_BisonBisonXBosTaurusHybridOrganism,
  MinorcaChickenOrganism: AnimalBreeds_MinorcaChickenOrganism,
  SaddlebredHorseSuperbreedOrganism: AnimalBreeds_SaddlebredHorseSuperbreedOrganism,
  AmericanSaddlebredHorseOrganism: AnimalBreeds_AmericanSaddlebredHorseOrganism,
  JerseyGiantChickenOrganism: AnimalBreeds_JerseyGiantChickenOrganism,
  ThoroughbredHorseOrganism: AnimalBreeds_ThoroughbredHorseOrganism,
  NorwegianLandracePigOrganism: AnimalBreeds_NorwegianLandracePigOrganism,
  YokohamaChickenOrganism: AnimalBreeds_YokohamaChickenOrganism,
  BoarPowerPig454Organism: AnimalBreeds_BoarPowerPig454Organism,
  RouenDuckBreedOrganism: AnimalBreeds_RouenDuckBreedOrganism,
  PolledDorsetSheepBreedOrganism: AnimalBreeds_PolledDorsetSheepBreedOrganism,
  RhinelanderHorseBreedOrganism: AnimalBreeds_RhinelanderHorseBreedOrganism,
  BrownSwissCattleBreedOrganism: AnimalBreeds_BrownSwissCattleBreedOrganism,
  TarpanHorseBreedOrganism: AnimalBreeds_TarpanHorseBreedOrganism,
  DebouilletSheepOrganism: AnimalBreeds_DebouilletSheepOrganism,
  MaineAnjouCattleBreedOrganism: AnimalBreeds_MaineAnjouCattleBreedOrganism,
  FHCElitePig1Organism: AnimalBreeds_FHCElitePig1Organism,
  MouflonSheepOrganism: AnimalBreeds_MouflonSheepOrganism,
  ShropshireSheepOrganism: AnimalBreeds_ShropshireSheepOrganism,
  AmericanAlbinoHorseOrganism: AnimalBreeds_AmericanAlbinoHorseOrganism,
  DelaineMerinoSheepOrganism: AnimalBreeds_DelaineMerinoSheepOrganism,
  AmericanPonyOrganism: AnimalBreeds_AmericanPonyOrganism,
  MidgetWhiteTurkeyOrganism: AnimalBreeds_MidgetWhiteTurkeyOrganism,
  GallusGallusOrganism: AnimalBreeds_GallusGallusOrganism,
  DarkBrownTurkeyOrganism: AnimalBreeds_DarkBrownTurkeyOrganism,
  JavaChickenOrganism: AnimalBreeds_JavaChickenOrganism,
  FHCElitePig4Organism: AnimalBreeds_FHCElitePig4Organism,
  CanadianHorseOrganism: AnimalBreeds_CanadianHorseOrganism,
  SilkieChickenOrganism: AnimalBreeds_SilkieChickenOrganism,
  BeltsvillePigOrganism: AnimalBreeds_BeltsvillePigOrganism,
  BoarPowerPig656Organism: AnimalBreeds_BoarPowerPig656Organism,
  ClunForestSheepOrganism: AnimalBreeds_ClunForestSheepOrganism,
  LimousinCattleBreedOrganism: AnimalBreeds_LimousinCattleBreedOrganism,
  PiedmonteseCattleBreedOrganism: AnimalBreeds_PiedmonteseCattleBreedOrganism,
  PalousePigOrganism: AnimalBreeds_PalousePigOrganism,
  LankenvelderChickenOrganism: AnimalBreeds_LankenvelderChickenOrganism,
  FHCElitePig6Organism: AnimalBreeds_FHCElitePig6Organism,
  PeruvianPasoHorseOrganism: AnimalBreeds_PeruvianPasoHorseOrganism,
  BlueSlateTurkeyOrganism: AnimalBreeds_BlueSlateTurkeyOrganism,
  CheviotSheepOrganism: AnimalBreeds_CheviotSheepOrganism,
  TarentaiseCattleBreedOrganism: AnimalBreeds_TarentaiseCattleBreedOrganism,
  TennesseeWalkingHorseOrganism: AnimalBreeds_TennesseeWalkingHorseOrganism,
  FayoumiChickenOrganism: AnimalBreeds_FayoumiChickenOrganism,
  MontdaleSheepOrganism: AnimalBreeds_MontdaleSheepOrganism,
  JerseyCattleBreedOrganism: AnimalBreeds_JerseyCattleBreedOrganism,
  OxfordSandyBlockPigOrganism: AnimalBreeds_OxfordSandyBlockPigOrganism,
  DominiqueChickenOrganism: AnimalBreeds_DominiqueChickenOrganism,
  DexterCattleBreedOrganism: AnimalBreeds_DexterCattleBreedOrganism,
  OxfordDownSheepOrganism: AnimalBreeds_OxfordDownSheepOrganism,
  BoarPowerPig59Organism: AnimalBreeds_BoarPowerPig59Organism,
  SantaGertrudisCattleBreedOrganism: AnimalBreeds_SantaGertrudisCattleBreedOrganism,
  RhodeIslandRedChickenOrganism: AnimalBreeds_RhodeIslandRedChickenOrganism,
  SuffolkHorseOrganism: AnimalBreeds_SuffolkHorseOrganism,
  ArabianHorseOrganism: AnimalBreeds_ArabianHorseOrganism,
  NewHampshireChickenOrganism: AnimalBreeds_NewHampshireChickenOrganism,
  WelshPigOrganism: AnimalBreeds_WelshPigOrganism,
  AustralorpChickenOrganism: AnimalBreeds_AustralorpChickenOrganism,
  FrenchAlpineGoatOrganism: AnimalBreeds_FrenchAlpineGoatOrganism,
  HaflingerHorseOrganism: AnimalBreeds_HaflingerHorseOrganism,
  GrayTurkeyOrganism: AnimalBreeds_GrayTurkeyOrganism,
  AmericanMiniatureHorseOrganism: AnimalBreeds_AmericanMiniatureHorseOrganism,
  JerseyBuffTurkeyOrganism: AnimalBreeds_JerseyBuffTurkeyOrganism,
  AppaloosaHorseOrganism: AnimalBreeds_AppaloosaHorseOrganism,
  LaManchaGoatOrganism: AnimalBreeds_LaManchaGoatOrganism,
  BoarPowerPig292Organism: AnimalBreeds_BoarPowerPig292Organism,
  PasoFinoHorseOrganism: AnimalBreeds_PasoFinoHorseOrganism,
  HollandChickenOrganism: AnimalBreeds_HollandChickenOrganism,
  BrahmaChickenOrganism: AnimalBreeds_BrahmaChickenOrganism,
  KleenLeenWhitePigOrganism: AnimalBreeds_KleenLeenWhitePigOrganism,
  SpitzhaubenChickenOrganism: AnimalBreeds_SpitzhaubenChickenOrganism,
  PintoOrganism: AnimalBreeds_PintoOrganism,
  DutchLandracePigOrganism: AnimalBreeds_DutchLandracePigOrganism,
  NavajoSheepOrganism: AnimalBreeds_NavajoSheepOrganism,
  FHCElitePig2Organism: AnimalBreeds_FHCElitePig2Organism,
  DekalbHybridPigLine33Organism: AnimalBreeds_DekalbHybridPigLine33Organism,
  LilacBuffTurkeyOrganism: AnimalBreeds_LilacBuffTurkeyOrganism,
  MarylandPigOrganism: AnimalBreeds_MarylandPigOrganism,
  BoarPowerPig545Organism: AnimalBreeds_BoarPowerPig545Organism,
  MinnesotaPigOrganism: AnimalBreeds_MinnesotaPigOrganism,
  MeusseRhineIjsselCattleBreedOrganism: AnimalBreeds_MeusseRhineIjsselCattleBreedOrganism,
  DorkingChickenOrganism: AnimalBreeds_DorkingChickenOrganism,
  CornishChickenOrganism: AnimalBreeds_CornishChickenOrganism,
  PolishChickenOrganism: AnimalBreeds_PolishChickenOrganism,
  DekalbHybridPigLine61Organism: AnimalBreeds_DekalbHybridPigLine61Organism,
  RoyalPalmTurkeyOrganism: AnimalBreeds_RoyalPalmTurkeyOrganism,
  BlondeDQuoteAquitaineOrganism: AnimalBreeds_BlondeDQuoteAquitaineOrganism,
  GalicenoHorseOrganism: AnimalBreeds_GalicenoHorseOrganism,
  ChanteclerChickenOrganism: AnimalBreeds_ChanteclerChickenOrganism,
  AngoraGoatOrganism: AnimalBreeds_AngoraGoatOrganism,
  LeicesterSheepOrganism: AnimalBreeds_LeicesterSheepOrganism,
  KarakulSheepOrganism: AnimalBreeds_KarakulSheepOrganism,
  LightBrownTurkeyOrganism: AnimalBreeds_LightBrownTurkeyOrganism,
  BlackFacedHighlandSheepOrganism: AnimalBreeds_BlackFacedHighlandSheepOrganism,
  MarchigianaCattleBreedOrganism: AnimalBreeds_MarchigianaCattleBreedOrganism,
  LincolnSheepOrganism: AnimalBreeds_LincolnSheepOrganism,
  BarnevelderChickenOrganism: AnimalBreeds_BarnevelderChickenOrganism,
  HanoverianHorseOrganism: AnimalBreeds_HanoverianHorseOrganism,
  LonghornCattleBreedOrganism: AnimalBreeds_LonghornCattleBreedOrganism,
  GallowayCattleBreedOrganism: AnimalBreeds_GallowayCattleBreedOrganism,
  SebrightChickenOrganism: AnimalBreeds_SebrightChickenOrganism,
  TurkenChickenOrganism: AnimalBreeds_TurkenChickenOrganism,
  CotswoldSheepOrganism: AnimalBreeds_CotswoldSheepOrganism,
  GermanFleckViehCattleBreedOrganism: AnimalBreeds_GermanFleckViehCattleBreedOrganism,
  CorriedaleSheepOrganism: AnimalBreeds_CorriedaleSheepOrganism,
  FHCElitePig5Organism: AnimalBreeds_FHCElitePig5Organism,
  PygmyGoatOrganism: AnimalBreeds_PygmyGoatOrganism,
  SultanChickenOrganism: AnimalBreeds_SultanChickenOrganism,
  BoarPowerPig141Organism: AnimalBreeds_BoarPowerPig141Organism,
  ShetlandPonyOrganism: AnimalBreeds_ShetlandPonyOrganism,
  ChesterWhitePigOrganism: AnimalBreeds_ChesterWhitePigOrganism,
  AnconaChickenOrganism: AnimalBreeds_AnconaChickenOrganism,
  KleenLeenPigOrganism: AnimalBreeds_KleenLeenPigOrganism,
  OrloffChickenOrganism: AnimalBreeds_OrloffChickenOrganism,
  LangshanChickenOrganism: AnimalBreeds_LangshanChickenOrganism,
  CubalayaChickenOrganism: AnimalBreeds_CubalayaChickenOrganism,
  GameChickenOrganism: AnimalBreeds_GameChickenOrganism,
  AlpineGoatOrganism: AnimalBreeds_AlpineGoatOrganism,
  IcelandicHorseOrganism: AnimalBreeds_IcelandicHorseOrganism,
  AnserAnserAnserOrganism: AnimalBreeds_AnserAnserAnserOrganism,
  CommercialBrownEggLayerTypeChickenOrganism: AnimalBreeds_CommercialBrownEggLayerTypeChickenOrganism,
  ItalianLandracePigOrganism: AnimalBreeds_ItalianLandracePigOrganism,
  FinnishLandraceSheepOrganism: AnimalBreeds_FinnishLandraceSheepOrganism,
  AmericanTunisHorseOrganism: AnimalBreeds_AmericanTunisHorseOrganism,
  SuffolkSheepOrganism: AnimalBreeds_SuffolkSheepOrganism,
  FHCElitePig8Organism: AnimalBreeds_FHCElitePig8Organism,
  PennaWhiteTurkeyOrganism: AnimalBreeds_PennaWhiteTurkeyOrganism,
  AmericanMerinoSheepOrganism: AnimalBreeds_AmericanMerinoSheepOrganism,
  WessexSaddlebackPigOrganism: AnimalBreeds_WessexSaddlebackPigOrganism,
  MinnesotaPigNumber1Organism: AnimalBreeds_MinnesotaPigNumber1Organism,
  MontanaPigOrganism: AnimalBreeds_MontanaPigOrganism,
  RedWattlePigOrganism: AnimalBreeds_RedWattlePigOrganism,
  RockAlpineGoatOrganism: AnimalBreeds_RockAlpineGoatOrganism,
  BoarPowerPigOrganism: AnimalBreeds_BoarPowerPigOrganism,
  DekalbHybridPigLineOrganism: AnimalBreeds_DekalbHybridPigLineOrganism,
  BoarPowerPig161Organism: AnimalBreeds_BoarPowerPig161Organism,
  PicPigOrganism: AnimalBreeds_PicPigOrganism,
  WyandotteChickenOrganism: AnimalBreeds_WyandotteChickenOrganism,
  AmericanPaintHorseOrganism: AnimalBreeds_AmericanPaintHorseOrganism,
  CampineChickenOrganism: AnimalBreeds_CampineChickenOrganism,
  SpanishMustangHorseOrganism: AnimalBreeds_SpanishMustangHorseOrganism,
  BoarPowerPig27Organism: AnimalBreeds_BoarPowerPig27Organism,
  QuarterHorseOrganism: AnimalBreeds_QuarterHorseOrganism,
  GelbveihCattleBreedOrganism: AnimalBreeds_GelbveihCattleBreedOrganism,
  NelloreCattleBreedOrganism: AnimalBreeds_NelloreCattleBreedOrganism,
  NebraskanSpottedTurkeyOrganism: AnimalBreeds_NebraskanSpottedTurkeyOrganism,
  LargeWhitePigOrganism: AnimalBreeds_LargeWhitePigOrganism,
  LamonaChickenOrganism: AnimalBreeds_LamonaChickenOrganism,
  ThuringerChickenOrganism: AnimalBreeds_ThuringerChickenOrganism,
  MalayChickenOrganism: AnimalBreeds_MalayChickenOrganism,
  AndalusianChickenOrganism: AnimalBreeds_AndalusianChickenOrganism,
  HornedHerefordOrganism: AnimalBreeds_HornedHerefordOrganism,
  MilkingShorthornCattleBreedOrganism: AnimalBreeds_MilkingShorthornCattleBreedOrganism,
  SusScrofaOrganism: AnimalBreeds_SusScrofaOrganism,
  BritishLandracePigOrganism: AnimalBreeds_BritishLandracePigOrganism,
  RomedaleSheepOrganism: AnimalBreeds_RomedaleSheepOrganism,
  PolandChinaPigOrganism: AnimalBreeds_PolandChinaPigOrganism,
  LuciePigOrganism: AnimalBreeds_LuciePigOrganism,
  LandracePigOrganism: AnimalBreeds_LandracePigOrganism,
  HungarianChickenOrganism: AnimalBreeds_HungarianChickenOrganism,
  NarragansettTurkeyOrganism: AnimalBreeds_NarragansettTurkeyOrganism,
  AndalusianHorseOrganism: AnimalBreeds_AndalusianHorseOrganism,
  ShorthornCattleBreedOrganism: AnimalBreeds_ShorthornCattleBreedOrganism,
  RosecombChickenOrganism: AnimalBreeds_RosecombChickenOrganism,
  WelshWalkingHorseOrganism: AnimalBreeds_WelshWalkingHorseOrganism,
  SanPierrePigOrganism: AnimalBreeds_SanPierrePigOrganism,
  SussexChickenOrganism: AnimalBreeds_SussexChickenOrganism,
  NormandieCattleBreedOrganism: AnimalBreeds_NormandieCattleBreedOrganism,
  CaliforniaChickenOrganism: AnimalBreeds_CaliforniaChickenOrganism,
  HampshireDownSheepOrganism: AnimalBreeds_HampshireDownSheepOrganism,
  HoudanChickenOrganism: AnimalBreeds_HoudanChickenOrganism,
  MiddleWhitePigOrganism: AnimalBreeds_MiddleWhitePigOrganism,
  ScottishHighlandCattleBreedOrganism: AnimalBreeds_ScottishHighlandCattleBreedOrganism,
  HungarianGooseOrganism: AnimalBreeds_HungarianGooseOrganism,
  CriolloCattleBreedOrganism: AnimalBreeds_CriolloCattleBreedOrganism,
  BoarPowerPig747Organism: AnimalBreeds_BoarPowerPig747Organism,
  KleenLeenRedPigOrganism: AnimalBreeds_KleenLeenRedPigOrganism,
  BoarPowerPig474Organism: AnimalBreeds_BoarPowerPig474Organism,
  DanishLandracePigOrganism: AnimalBreeds_DanishLandracePigOrganism,
  GuernseyCattleBreedOrganism: AnimalBreeds_GuernseyCattleBreedOrganism,
  AberdeenAngusCattleBreedOrganism: AnimalBreeds_AberdeenAngusCattleBreedOrganism,
  DekalbHybridPigLine51Organism: AnimalBreeds_DekalbHybridPigLine51Organism,
  YorkshirePigOrganism: AnimalBreeds_YorkshirePigOrganism,
  EquusAsinusOrganism: AnimalBreeds_EquusAsinusOrganism,
  PicLinePig24Organism: AnimalBreeds_PicLinePig24Organism,
  HormelMiniaturePigOrganism: AnimalBreeds_HormelMiniaturePigOrganism,
  HornedDorsetSheepBreedOrganism: AnimalBreeds_HornedDorsetSheepBreedOrganism,
  BoarPowerPig282Organism: AnimalBreeds_BoarPowerPig282Organism,
  WelsummerChickenOrganism: AnimalBreeds_WelsummerChickenOrganism,
  GermanLandracePigOrganism: AnimalBreeds_GermanLandracePigOrganism,
  SumatraChickenOrganism: AnimalBreeds_SumatraChickenOrganism,
  NorthCountyCheviotSheepOrganism: AnimalBreeds_NorthCountyCheviotSheepOrganism,
  RedPollCattleBreedOrganism: AnimalBreeds_RedPollCattleBreedOrganism,
  FjordHorseOrganism: AnimalBreeds_FjordHorseOrganism,
  TargheeSheepOrganism: AnimalBreeds_TargheeSheepOrganism,
  FrenchLandracePigOrganism: AnimalBreeds_FrenchLandracePigOrganism,
  ChamoiseeGoatOrganism: AnimalBreeds_ChamoiseeGoatOrganism,
  AyrshireCattleBreedOrganism: AnimalBreeds_AyrshireCattleBreedOrganism,
  FHCPigOrganism: AnimalBreeds_FHCPigOrganism,
  AmericanBuckskinHorseOrganism: AnimalBreeds_AmericanBuckskinHorseOrganism,
  SalersCattleBreedOrganism: AnimalBreeds_SalersCattleBreedOrganism,
  GloucesterOldSpotPigOrganism: AnimalBreeds_GloucesterOldSpotPigOrganism,
  OICPigOrganism: AnimalBreeds_OICPigOrganism,
  BlackSpanishTurkeyOrganism: AnimalBreeds_BlackSpanishTurkeyOrganism,
  FrizzleChickenOrganism: AnimalBreeds_FrizzleChickenOrganism,
  ChineseGooseOrganism: AnimalBreeds_ChineseGooseOrganism,
  CamarronGoatOrganism: AnimalBreeds_CamarronGoatOrganism,
  PolledHerefordOrganism: AnimalBreeds_PolledHerefordOrganism,
  BrownWelshCattleBreedOrganism: AnimalBreeds_BrownWelshCattleBreedOrganism,
};
const animal_genderstatus_Intact: Coding = {
  code: 'intact',
  display: 'Intact',
  system: 'http://hl7.org/fhir/animal-genderstatus',
};
const animal_genderstatus_Neutered: Coding = {
  code: 'neutered',
  display: 'Neutered',
  system: 'http://hl7.org/fhir/animal-genderstatus',
};
const animal_genderstatus_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/animal-genderstatus',
};
/**
 * This example value set defines a set of codes that can be used to indicate the current state of the animal's reproductive organs.
 */
export const AnimalGenderstatus = {
  /**
   * The animal's reproductive organs are intact.
   */
  Intact: animal_genderstatus_Intact,
  /**
   * The animal has been sterilized, castrated or otherwise made infertile.
   */
  Neutered: animal_genderstatus_Neutered,
  /**
   * Unable to determine whether the animal has been neutered.
   */
  Unknown: animal_genderstatus_Unknown,
};
const AnimalSpecies_Mule: Coding = {
  code: '132950000',
  display: 'mule',
  system: 'http://snomed.info/sct',
};
const AnimalSpecies_Goose: Coding = {
  code: '15778005',
  display: 'goose',
  system: 'http://snomed.info/sct',
};
const AnimalSpecies_Horse: Coding = {
  code: '388445009',
  display: 'horse',
  system: 'http://snomed.info/sct',
};
const AnimalSpecies_Duck: Coding = {
  code: '396620009',
  display: 'duck',
  system: 'http://snomed.info/sct',
};
const AnimalSpecies_Turkey: Coding = {
  code: '425134008',
  display: 'turkey',
  system: 'http://snomed.info/sct',
};
/**
 * This example value set defines a set of codes that can be used to indicate species of animal patients.
 */
export const AnimalSpecies = {
  CapraHircusOrganism: AnimalBreeds_CapraHircusOrganism,
  OvisAriesOrganism: AnimalBreeds_OvisAriesOrganism,
  Mule: AnimalSpecies_Mule,
  Goose: AnimalSpecies_Goose,
  BosTaurusOrganism: AnimalBreeds_BosTaurusOrganism,
  Horse: AnimalSpecies_Horse,
  Duck: AnimalSpecies_Duck,
  Turkey: AnimalSpecies_Turkey,
  GallusGallusOrganism: AnimalBreeds_GallusGallusOrganism,
  EquusAsinusOrganism: AnimalBreeds_EquusAsinusOrganism,
};
const appointment_cancellation_reason_EquipmentMaintenanceRepair: Coding = {
  code: 'maint',
  display: 'Equipment Maintenance/Repair',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_PrepMedIncomplete: Coding = {
  code: 'meds-inc',
  display: 'Prep/Med Incomplete',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_OtherCMSTherapyCapServiceNotAuthorized: Coding = {
  code: 'oth-cms',
  display: 'Other: CMS Therapy Cap Service Not Authorized',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_OtherError: Coding = {
  code: 'oth-err',
  display: 'Other: Error',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_OtherFinancial: Coding = {
  code: 'oth-fin',
  display: 'Other: Financial',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_OtherNoInterpreterAvailable: Coding = {
  code: 'oth-int',
  display: 'Other: No Interpreter Available',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_OtherImproperIVAccessInfiltrateIV: Coding = {
  code: 'oth-iv',
  display: 'Other: Improper IV Access/Infiltrate IV',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_OtherPrepMedResultsUnavailable: Coding = {
  code: 'oth-mu',
  display: 'Other: Prep/Med/Results Unavailable',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_OtherScheduleOrderError: Coding = {
  code: 'oth-oerr',
  display: 'Other: Schedule Order Error',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_OtherRoomResourceMaintenance: Coding = {
  code: 'oth-room',
  display: 'Other: Room/Resource Maintenance',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_OtherSilentWalkInError: Coding = {
  code: 'oth-swie',
  display: 'Other: Silent Walk In Error',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_OtherWeather: Coding = {
  code: 'oth-weath',
  display: 'Other: Weather',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_Patient: Coding = {
  code: 'pat',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_PatientCanceledViaPatientPortal: Coding = {
  code: 'pat-cpp',
  display: 'Patient: Canceled via Patient Portal',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_PatientCanceledViaAutomatedReminderSystem: Coding = {
  code: 'pat-crs',
  display: 'Patient: Canceled via automated reminder system',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_PatientDeceased: Coding = {
  code: 'pat-dec',
  display: 'Patient: Deceased',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_PatientFeelingBetter: Coding = {
  code: 'pat-fb',
  display: 'Patient: Feeling Better',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_PatientLackOfTransportation: Coding = {
  code: 'pat-lt',
  display: 'Patient: Lack of Transportation',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_PatientMemberTerminated: Coding = {
  code: 'pat-mt',
  display: 'Patient: Member Terminated',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_PatientMoved: Coding = {
  code: 'pat-mv',
  display: 'Patient: Moved',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_PatientPregnant: Coding = {
  code: 'pat-preg',
  display: 'Patient: Pregnant',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_PatientScheduledFromWaitList: Coding = {
  code: 'pat-swl',
  display: 'Patient: Scheduled from Wait List',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_PatientUnhappyChangedProvider: Coding = {
  code: 'pat-ucp',
  display: 'Patient: Unhappy/Changed Provider',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_Provider: Coding = {
  code: 'prov',
  display: 'Provider',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_ProviderDischarged: Coding = {
  code: 'prov-dch',
  display: 'Provider: Discharged',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_ProviderEduMeeting: Coding = {
  code: 'prov-edu',
  display: 'Provider: Edu/Meeting',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_ProviderHospitalized: Coding = {
  code: 'prov-hosp',
  display: 'Provider: Hospitalized',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_ProviderLabsOutOfAcceptableRange: Coding = {
  code: 'prov-labs',
  display: 'Provider: Labs Out of Acceptable Range',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_ProviderMRIScreeningFormMarkedDoNotProceed: Coding = {
  code: 'prov-mri',
  display: 'Provider: MRI Screening Form Marked Do Not Proceed',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_ProviderOncologyTreatmentPlanChanges: Coding = {
  code: 'prov-onc',
  display: 'Provider: Oncology Treatment Plan Changes',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
const appointment_cancellation_reason_ProviderPersonal: Coding = {
  code: 'prov-pers',
  display: 'Provider: Personal',
  system: 'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason',
};
/**
 * This example value set defines a set of reasons for the cancellation of an appointment.
 */
export const AppointmentCancellationReason = {
  EquipmentMaintenanceRepair: appointment_cancellation_reason_EquipmentMaintenanceRepair,
  PrepMedIncomplete: appointment_cancellation_reason_PrepMedIncomplete,
  OtherCMSTherapyCapServiceNotAuthorized: appointment_cancellation_reason_OtherCMSTherapyCapServiceNotAuthorized,
  Other: appointment_cancellation_reason_Other,
  OtherError: appointment_cancellation_reason_OtherError,
  OtherFinancial: appointment_cancellation_reason_OtherFinancial,
  OtherNoInterpreterAvailable: appointment_cancellation_reason_OtherNoInterpreterAvailable,
  OtherImproperIVAccessInfiltrateIV: appointment_cancellation_reason_OtherImproperIVAccessInfiltrateIV,
  OtherPrepMedResultsUnavailable: appointment_cancellation_reason_OtherPrepMedResultsUnavailable,
  OtherScheduleOrderError: appointment_cancellation_reason_OtherScheduleOrderError,
  OtherRoomResourceMaintenance: appointment_cancellation_reason_OtherRoomResourceMaintenance,
  OtherSilentWalkInError: appointment_cancellation_reason_OtherSilentWalkInError,
  OtherWeather: appointment_cancellation_reason_OtherWeather,
  Patient: appointment_cancellation_reason_Patient,
  PatientCanceledViaPatientPortal: appointment_cancellation_reason_PatientCanceledViaPatientPortal,
  PatientCanceledViaAutomatedReminderSystem: appointment_cancellation_reason_PatientCanceledViaAutomatedReminderSystem,
  PatientDeceased: appointment_cancellation_reason_PatientDeceased,
  PatientFeelingBetter: appointment_cancellation_reason_PatientFeelingBetter,
  PatientLackOfTransportation: appointment_cancellation_reason_PatientLackOfTransportation,
  PatientMemberTerminated: appointment_cancellation_reason_PatientMemberTerminated,
  PatientMoved: appointment_cancellation_reason_PatientMoved,
  PatientPregnant: appointment_cancellation_reason_PatientPregnant,
  PatientScheduledFromWaitList: appointment_cancellation_reason_PatientScheduledFromWaitList,
  PatientUnhappyChangedProvider: appointment_cancellation_reason_PatientUnhappyChangedProvider,
  Provider: appointment_cancellation_reason_Provider,
  ProviderDischarged: appointment_cancellation_reason_ProviderDischarged,
  ProviderEduMeeting: appointment_cancellation_reason_ProviderEduMeeting,
  ProviderHospitalized: appointment_cancellation_reason_ProviderHospitalized,
  ProviderLabsOutOfAcceptableRange: appointment_cancellation_reason_ProviderLabsOutOfAcceptableRange,
  ProviderMRIScreeningFormMarkedDoNotProceed: appointment_cancellation_reason_ProviderMRIScreeningFormMarkedDoNotProceed,
  ProviderOncologyTreatmentPlanChanges: appointment_cancellation_reason_ProviderOncologyTreatmentPlanChanges,
  ProviderPersonal: appointment_cancellation_reason_ProviderPersonal,
};
const appointmentstatus_Arrived: Coding = {
  code: 'arrived',
  display: 'Arrived',
  system: 'http://hl7.org/fhir/appointmentstatus',
};
const appointmentstatus_Booked: Coding = {
  code: 'booked',
  display: 'Booked',
  system: 'http://hl7.org/fhir/appointmentstatus',
};
const appointmentstatus_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/appointmentstatus',
};
const appointmentstatus_CheckedIn: Coding = {
  code: 'checked-in',
  display: 'Checked In',
  system: 'http://hl7.org/fhir/appointmentstatus',
};
const appointmentstatus_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in error',
  system: 'http://hl7.org/fhir/appointmentstatus',
};
const appointmentstatus_Fulfilled: Coding = {
  code: 'fulfilled',
  display: 'Fulfilled',
  system: 'http://hl7.org/fhir/appointmentstatus',
};
const appointmentstatus_NoShow: Coding = {
  code: 'noshow',
  display: 'No Show',
  system: 'http://hl7.org/fhir/appointmentstatus',
};
const appointmentstatus_Pending: Coding = {
  code: 'pending',
  display: 'Pending',
  system: 'http://hl7.org/fhir/appointmentstatus',
};
const appointmentstatus_Proposed: Coding = {
  code: 'proposed',
  display: 'Proposed',
  system: 'http://hl7.org/fhir/appointmentstatus',
};
const appointmentstatus_Waitlisted: Coding = {
  code: 'waitlist',
  display: 'Waitlisted',
  system: 'http://hl7.org/fhir/appointmentstatus',
};
/**
 * The free/busy status of an appointment.
 */
export const Appointmentstatus = {
  /**
   * The patient/patients has/have arrived and is/are waiting to be seen.
   */
  Arrived: appointmentstatus_Arrived,
  /**
   * All participant(s) have been considered and the appointment is confirmed to go ahead at the date/times specified.
   */
  Booked: appointmentstatus_Booked,
  /**
   * The appointment has been cancelled.
   */
  Cancelled: appointmentstatus_Cancelled,
  /**
   * When checked in, all pre-encounter administrative work is complete, and the encounter may begin. (where multiple patients are involved, they are all present).
   */
  CheckedIn: appointmentstatus_CheckedIn,
  /**
   * This instance should not have been part of this patient's medical record.
   */
  EnteredInError: appointmentstatus_EnteredInError,
  /**
   * The planning stages of the appointment are now complete, the encounter resource will exist and will track further status changes. Note that an encounter may exist before the appointment status is fulfilled for many reasons.
   */
  Fulfilled: appointmentstatus_Fulfilled,
  /**
   * Some or all of the participant(s) have not/did not appear for the appointment (usually the patient).
   */
  NoShow: appointmentstatus_NoShow,
  /**
   * Some or all of the participant(s) have not finalized their acceptance of the appointment request.
   */
  Pending: appointmentstatus_Pending,
  /**
   * None of the participant(s) have finalized their acceptance of the appointment request, and the start/end time might not be set yet.
   */
  Proposed: appointmentstatus_Proposed,
  /**
   * The appointment has been placed on a waitlist, to be scheduled/confirmed in the future when a slot/service is available.
   * A specific time might or might not be pre-allocated.
   */
  Waitlisted: appointmentstatus_Waitlisted,
};
const assert_direction_codes_Request: Coding = {
  code: 'request',
  display: 'request',
  system: 'http://hl7.org/fhir/assert-direction-codes',
};
const assert_direction_codes_Response: Coding = {
  code: 'response',
  display: 'response',
  system: 'http://hl7.org/fhir/assert-direction-codes',
};
/**
 * The type of direction to use for assertion.
 */
export const AssertDirectionCodes = {
  /**
   * The assertion is evaluated on the request.
   */
  Request: assert_direction_codes_Request,
  /**
   * The assertion is evaluated on the response. This is the default value.
   */
  Response: assert_direction_codes_Response,
};
const assert_operator_codes_Contains: Coding = {
  code: 'contains',
  display: 'contains',
  system: 'http://hl7.org/fhir/assert-operator-codes',
};
const assert_operator_codes_Empty: Coding = {
  code: 'empty',
  display: 'empty',
  system: 'http://hl7.org/fhir/assert-operator-codes',
};
const assert_operator_codes_Equals: Coding = {
  code: 'equals',
  display: 'equals',
  system: 'http://hl7.org/fhir/assert-operator-codes',
};
const assert_operator_codes_Evaluate: Coding = {
  code: 'eval',
  display: 'evaluate',
  system: 'http://hl7.org/fhir/assert-operator-codes',
};
const assert_operator_codes_GreaterThan: Coding = {
  code: 'greaterThan',
  display: 'greaterThan',
  system: 'http://hl7.org/fhir/assert-operator-codes',
};
const assert_operator_codes_In: Coding = {
  code: 'in',
  display: 'in',
  system: 'http://hl7.org/fhir/assert-operator-codes',
};
const assert_operator_codes_LessThan: Coding = {
  code: 'lessThan',
  display: 'lessThan',
  system: 'http://hl7.org/fhir/assert-operator-codes',
};
const assert_operator_codes_NotContains: Coding = {
  code: 'notContains',
  display: 'notContains',
  system: 'http://hl7.org/fhir/assert-operator-codes',
};
const assert_operator_codes_NotEmpty: Coding = {
  code: 'notEmpty',
  display: 'notEmpty',
  system: 'http://hl7.org/fhir/assert-operator-codes',
};
const assert_operator_codes_NotEquals: Coding = {
  code: 'notEquals',
  display: 'notEquals',
  system: 'http://hl7.org/fhir/assert-operator-codes',
};
const assert_operator_codes_NotIn: Coding = {
  code: 'notIn',
  display: 'notIn',
  system: 'http://hl7.org/fhir/assert-operator-codes',
};
/**
 * The type of operator to use for assertion.
 */
export const AssertOperatorCodes = {
  /**
   * Compare value string contains a known value.
   */
  Contains: assert_operator_codes_Contains,
  /**
   * Compare value is empty.
   */
  Empty: assert_operator_codes_Empty,
  /**
   * Default value. Equals comparison.
   */
  Equals: assert_operator_codes_Equals,
  /**
   * Evaluate the FHIRPath expression as a boolean condition.
   */
  Evaluate: assert_operator_codes_Evaluate,
  /**
   * Compare value to be greater than a known value.
   */
  GreaterThan: assert_operator_codes_GreaterThan,
  /**
   * Compare value within a known set of values.
   */
  In: assert_operator_codes_In,
  /**
   * Compare value to be less than a known value.
   */
  LessThan: assert_operator_codes_LessThan,
  /**
   * Compare value string does not contain a known value.
   */
  NotContains: assert_operator_codes_NotContains,
  /**
   * Compare value is not empty.
   */
  NotEmpty: assert_operator_codes_NotEmpty,
  /**
   * Not equals comparison.
   */
  NotEquals: assert_operator_codes_NotEquals,
  /**
   * Compare value not within a known set of values.
   */
  NotIn: assert_operator_codes_NotIn,
};
const assert_response_code_types_Bad: Coding = {
  code: 'bad',
  display: 'bad',
  system: 'http://hl7.org/fhir/assert-response-code-types',
};
const assert_response_code_types_Conflict: Coding = {
  code: 'conflict',
  display: 'conflict',
  system: 'http://hl7.org/fhir/assert-response-code-types',
};
const assert_response_code_types_Created: Coding = {
  code: 'created',
  display: 'created',
  system: 'http://hl7.org/fhir/assert-response-code-types',
};
const assert_response_code_types_Forbidden: Coding = {
  code: 'forbidden',
  display: 'forbidden',
  system: 'http://hl7.org/fhir/assert-response-code-types',
};
const assert_response_code_types_Gone: Coding = {
  code: 'gone',
  display: 'gone',
  system: 'http://hl7.org/fhir/assert-response-code-types',
};
const assert_response_code_types_MethodNotAllowed: Coding = {
  code: 'methodNotAllowed',
  display: 'methodNotAllowed',
  system: 'http://hl7.org/fhir/assert-response-code-types',
};
const assert_response_code_types_NoContent: Coding = {
  code: 'noContent',
  display: 'noContent',
  system: 'http://hl7.org/fhir/assert-response-code-types',
};
const assert_response_code_types_NotFound: Coding = {
  code: 'notFound',
  display: 'notFound',
  system: 'http://hl7.org/fhir/assert-response-code-types',
};
const assert_response_code_types_NotModified: Coding = {
  code: 'notModified',
  display: 'notModified',
  system: 'http://hl7.org/fhir/assert-response-code-types',
};
const assert_response_code_types_Okay: Coding = {
  code: 'okay',
  display: 'okay',
  system: 'http://hl7.org/fhir/assert-response-code-types',
};
const assert_response_code_types_PreconditionFailed: Coding = {
  code: 'preconditionFailed',
  display: 'preconditionFailed',
  system: 'http://hl7.org/fhir/assert-response-code-types',
};
const assert_response_code_types_Unprocessable: Coding = {
  code: 'unprocessable',
  display: 'unprocessable',
  system: 'http://hl7.org/fhir/assert-response-code-types',
};
/**
 * The type of response code to use for assertion.
 */
export const AssertResponseCodeTypes = {
  /**
   * Response code is 400.
   */
  Bad: assert_response_code_types_Bad,
  /**
   * Response code is 409.
   */
  Conflict: assert_response_code_types_Conflict,
  /**
   * Response code is 201.
   */
  Created: assert_response_code_types_Created,
  /**
   * Response code is 403.
   */
  Forbidden: assert_response_code_types_Forbidden,
  /**
   * Response code is 410.
   */
  Gone: assert_response_code_types_Gone,
  /**
   * Response code is 405.
   */
  MethodNotAllowed: assert_response_code_types_MethodNotAllowed,
  /**
   * Response code is 204.
   */
  NoContent: assert_response_code_types_NoContent,
  /**
   * Response code is 404.
   */
  NotFound: assert_response_code_types_NotFound,
  /**
   * Response code is 304.
   */
  NotModified: assert_response_code_types_NotModified,
  /**
   * Response code is 200.
   */
  Okay: assert_response_code_types_Okay,
  /**
   * Response code is 412.
   */
  PreconditionFailed: assert_response_code_types_PreconditionFailed,
  /**
   * Response code is 422.
   */
  Unprocessable: assert_response_code_types_Unprocessable,
};
const asset_availability_Lease: Coding = {
  code: 'lease',
  display: 'Lease',
  system: 'http://hl7.org/fhir/asset-availability',
};
/**
 * This value set has asset availability codes.
 */
export const AssetAvailability = {
  /**
   * To be completed
   */
  Lease: asset_availability_Lease,
};
const audit_entity_type_Person: Coding = {
  code: '1',
  display: 'Person',
  system: 'http://terminology.hl7.org/CodeSystem/audit-entity-type',
};
const audit_entity_type_SystemObject: Coding = {
  code: '2',
  display: 'System Object',
  system: 'http://terminology.hl7.org/CodeSystem/audit-entity-type',
};
const audit_entity_type_Organization: Coding = {
  code: '3',
  display: 'Organization',
  system: 'http://terminology.hl7.org/CodeSystem/audit-entity-type',
};
const audit_entity_type_Other: Coding = {
  code: '4',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/audit-entity-type',
};
/**
 * Code for the entity type involved in the audit event.
 */
export const AuditEntityType = {
  /**
   * Person
   */
  Person_audit_entity_type: audit_entity_type_Person,
  /**
   * System Object
   */
  SystemObject_audit_entity_type: audit_entity_type_SystemObject,
  /**
   * Organization
   */
  Organization_audit_entity_type: audit_entity_type_Organization,
  /**
   * Other
   */
  Other_audit_entity_type: audit_entity_type_Other,
  /**
   * A financial tool for tracking value accrued for a particular purpose.  In the healthcare field, used to track charges for a patient, cost centers, etc.
   */
  Account_resource_types: resource_types_Account,
  /**
   * This resource allows for the definition of some activity to be performed, independent of a particular patient, practitioner, or other performance context.
   */
  ActivityDefinition_resource_types: resource_types_ActivityDefinition,
  /**
   * Actual or  potential/avoided event causing unintended physical injury resulting from or contributed to by medical care, a research study or other healthcare setting factors that requires additional monitoring, treatment, or hospitalization, or that results in death.
   */
  AdverseEvent_resource_types: resource_types_AdverseEvent,
  /**
   * Risk of harmful or undesirable, physiological response which is unique to an individual and associated with exposure to a substance.
   */
  AllergyIntolerance_resource_types: resource_types_AllergyIntolerance,
  /**
   * A booking of a healthcare event among patient(s), practitioner(s), related person(s) and/or device(s) for a specific date/time. This may result in one or more Encounter(s).
   */
  Appointment_resource_types: resource_types_Appointment,
  /**
   * A reply to an appointment request for a patient and/or practitioner(s), such as a confirmation or rejection.
   */
  AppointmentResponse_resource_types: resource_types_AppointmentResponse,
  /**
   * A record of an event made for purposes of maintaining a security log. Typical uses include detection of intrusion attempts and monitoring for inappropriate usage.
   */
  AuditEvent_resource_types: resource_types_AuditEvent,
  /**
   * Basic is used for handling concepts not yet defined in FHIR, narrative-only resources that don't map to an existing resource, and custom resources not appropriate for inclusion in the FHIR specification.
   */
  Basic_resource_types: resource_types_Basic,
  /**
   * A resource that represents the data of a single raw artifact as digital content accessible in its native format.  A Binary resource can contain any content, whether text, image, pdf, zip archive, etc.
   */
  Binary_resource_types: resource_types_Binary,
  /**
   * A material substance originating from a biological entity intended to be transplanted or infused
   * into another (possibly the same) biological entity.
   */
  BiologicallyDerivedProduct_resource_types: resource_types_BiologicallyDerivedProduct,
  /**
   * Record details about an anatomical structure.  This resource may be used when a coded concept does not provide the necessary detail needed for the use case.
   */
  BodyStructure_resource_types: resource_types_BodyStructure,
  /**
   * A container for a collection of resources.
   */
  Bundle_resource_types: resource_types_Bundle,
  /**
   * A Capability Statement documents a set of capabilities (behaviors) of a FHIR Server for a particular version of FHIR that may be used as a statement of actual server functionality or a statement of required or desired server implementation.
   */
  CapabilityStatement_resource_types: resource_types_CapabilityStatement,
  /**
   * Describes the intention of how one or more practitioners intend to deliver care for a particular patient, group or community for a period of time, possibly limited to care for a specific condition or set of conditions.
   */
  CarePlan_resource_types: resource_types_CarePlan,
  /**
   * The Care Team includes all the people and organizations who plan to participate in the coordination and delivery of care for a patient.
   */
  CareTeam_resource_types: resource_types_CareTeam,
  /**
   * Catalog entries are wrappers that contextualize items included in a catalog.
   */
  CatalogEntry_resource_types: resource_types_CatalogEntry,
  /**
   * The resource ChargeItem describes the provision of healthcare provider products for a certain patient, therefore referring not only to the product, but containing in addition details of the provision, like date, time, amounts and participating organizations and persons. Main Usage of the ChargeItem is to enable the billing process and internal cost allocation.
   */
  ChargeItem_resource_types: resource_types_ChargeItem,
  /**
   * The ChargeItemDefinition resource provides the properties that apply to the (billing) codes necessary to calculate costs and prices. The properties may differ largely depending on type and realm, therefore this resource gives only a rough structure and requires profiling for each type of billing code system.
   */
  ChargeItemDefinition_resource_types: resource_types_ChargeItemDefinition,
  /**
   * A provider issued list of professional services and products which have been provided, or are to be provided, to a patient which is sent to an insurer for reimbursement.
   */
  Claim_resource_types: resource_types_Claim,
  /**
   * This resource provides the adjudication details from the processing of a Claim resource.
   */
  ClaimResponse_resource_types: resource_types_ClaimResponse,
  /**
   * A record of a clinical assessment performed to determine what problem(s) may affect the patient and before planning the treatments or management strategies that are best to manage a patient's condition. Assessments are often 1:1 with a clinical consultation / encounter,  but this varies greatly depending on the clinical workflow. This resource is called "ClinicalImpression" rather than "ClinicalAssessment" to avoid confusion with the recording of assessment tools such as Apgar score.
   */
  ClinicalImpression_resource_types: resource_types_ClinicalImpression,
  /**
   * The CodeSystem resource is used to declare the existence of and describe a code system or code system supplement and its key properties, and optionally define a part or all of its content.
   */
  CodeSystem_resource_types: resource_types_CodeSystem,
  /**
   * An occurrence of information being transmitted; e.g. an alert that was sent to a responsible provider, a public health agency that was notified about a reportable condition.
   */
  Communication_resource_types: resource_types_Communication,
  /**
   * A request to convey information; e.g. the CDS system proposes that an alert be sent to a responsible provider, the CDS system proposes that the public health agency be notified about a reportable condition.
   */
  CommunicationRequest_resource_types: resource_types_CommunicationRequest,
  /**
   * A compartment definition that defines how resources are accessed on a server.
   */
  CompartmentDefinition_resource_types: resource_types_CompartmentDefinition,
  /**
   * A set of healthcare-related information that is assembled together into a single logical package that provides a single coherent statement of meaning, establishes its own context and that has clinical attestation with regard to who is making the statement. A Composition defines the structure and narrative content necessary for a document. However, a Composition alone does not constitute a document. Rather, the Composition must be the first entry in a Bundle where Bundle.type=document, and any other resources referenced from Composition must be included as subsequent entries in the Bundle (for example Patient, Practitioner, Encounter, etc.).
   */
  Composition_resource_types: resource_types_Composition,
  /**
   * A statement of relationships from one set of concepts to one or more other concepts - either concepts in code systems, or data element/data element concepts, or classes in class models.
   */
  ConceptMap_resource_types: resource_types_ConceptMap,
  /**
   * A clinical condition, problem, diagnosis, or other event, situation, issue, or clinical concept that has risen to a level of concern.
   */
  Condition_resource_types: resource_types_Condition,
  /**
   * A record of a healthcare consumer’s  choices, which permits or denies identified recipient(s) or recipient role(s) to perform one or more actions within a given policy context, for specific purposes and periods of time.
   */
  Consent_resource_types: resource_types_Consent,
  /**
   * Legally enforceable, formally recorded unilateral or bilateral directive i.e., a policy or agreement.
   */
  Contract_resource_types: resource_types_Contract,
  /**
   * Financial instrument which may be used to reimburse or pay for health care products and services. Includes both insurance and self-payment.
   */
  Coverage_resource_types: resource_types_Coverage,
  /**
   * The CoverageEligibilityRequest provides patient and insurance coverage information to an insurer for them to respond, in the form of an CoverageEligibilityResponse, with information regarding whether the stated coverage is valid and in-force and optionally to provide the insurance details of the policy.
   */
  CoverageEligibilityRequest_resource_types: resource_types_CoverageEligibilityRequest,
  /**
   * This resource provides eligibility and plan details from the processing of an CoverageEligibilityRequest resource.
   */
  CoverageEligibilityResponse_resource_types: resource_types_CoverageEligibilityResponse,
  /**
   * Indicates an actual or potential clinical issue with or between one or more active or proposed clinical actions for a patient; e.g. Drug-drug interaction, Ineffective treatment frequency, Procedure-condition conflict, etc.
   */
  DetectedIssue_resource_types: resource_types_DetectedIssue,
  /**
   * A type of a manufactured item that is used in the provision of healthcare without being substantially changed through that activity. The device may be a medical or non-medical device.
   */
  Device_resource_types: resource_types_Device,
  /**
   * The characteristics, operational status and capabilities of a medical-related component of a medical device.
   */
  DeviceDefinition_resource_types: resource_types_DeviceDefinition,
  /**
   * Describes a measurement, calculation or setting capability of a medical device.
   */
  DeviceMetric_resource_types: resource_types_DeviceMetric,
  /**
   * Represents a request for a patient to employ a medical device. The device may be an implantable device, or an external assistive device, such as a walker.
   */
  DeviceRequest_resource_types: resource_types_DeviceRequest,
  /**
   * A record of a device being used by a patient where the record is the result of a report from the patient or another clinician.
   */
  DeviceUseStatement_resource_types: resource_types_DeviceUseStatement,
  /**
   * The findings and interpretation of diagnostic  tests performed on patients, groups of patients, devices, and locations, and/or specimens derived from these. The report includes clinical context such as requesting and provider information, and some mix of atomic results, images, textual and coded interpretations, and formatted representation of diagnostic reports.
   */
  DiagnosticReport_resource_types: resource_types_DiagnosticReport,
  /**
   * A collection of documents compiled for a purpose together with metadata that applies to the collection.
   */
  DocumentManifest_resource_types: resource_types_DocumentManifest,
  /**
   * A reference to a document of any kind for any purpose. Provides metadata about the document so that the document can be discovered and managed. The scope of a document is any seralized object with a mime-type, so includes formal patient centric documents (CDA), cliical notes, scanned paper, and non-patient specific documents like policy text.
   */
  DocumentReference_resource_types: resource_types_DocumentReference,
  /**
   * A resource that includes narrative, extensions, and contained resources.
   */
  DomainResource_resource_types: resource_types_DomainResource,
  /**
   * The EffectEvidenceSynthesis resource describes the difference in an outcome between exposures states in a population where the effect estimate is derived from a combination of research studies.
   */
  EffectEvidenceSynthesis_resource_types: resource_types_EffectEvidenceSynthesis,
  /**
   * An interaction between a patient and healthcare provider(s) for the purpose of providing healthcare service(s) or assessing the health status of a patient.
   */
  Encounter_resource_types: resource_types_Encounter,
  /**
   * The technical details of an endpoint that can be used for electronic services, such as for web services providing XDS.b or a REST endpoint for another FHIR server. This may include any security context information.
   */
  Endpoint_resource_types: resource_types_Endpoint,
  /**
   * This resource provides the insurance enrollment details to the insurer regarding a specified coverage.
   */
  EnrollmentRequest_resource_types: resource_types_EnrollmentRequest,
  /**
   * This resource provides enrollment and plan details from the processing of an EnrollmentRequest resource.
   */
  EnrollmentResponse_resource_types: resource_types_EnrollmentResponse,
  /**
   * An association between a patient and an organization / healthcare provider(s) during which time encounters may occur. The managing organization assumes a level of responsibility for the patient during this time.
   */
  EpisodeOfCare_resource_types: resource_types_EpisodeOfCare,
  /**
   * The EventDefinition resource provides a reusable description of when a particular event can occur.
   */
  EventDefinition_resource_types: resource_types_EventDefinition,
  /**
   * The Evidence resource describes the conditional state (population and any exposures being compared within the population) and outcome (if specified) that the knowledge (evidence, assertion, recommendation) is about.
   */
  Evidence_resource_types: resource_types_Evidence,
  /**
   * The EvidenceVariable resource describes a "PICO" element that knowledge (evidence, assertion, recommendation) is about.
   */
  EvidenceVariable_resource_types: resource_types_EvidenceVariable,
  /**
   * Example of workflow instance.
   */
  ExampleScenario_resource_types: resource_types_ExampleScenario,
  /**
   * This resource provides: the claim details; adjudication details from the processing of a Claim; and optionally account balance information, for informing the subscriber of the benefits provided.
   */
  ExplanationOfBenefit_resource_types: resource_types_ExplanationOfBenefit,
  /**
   * Significant health conditions for a person related to the patient relevant in the context of care for the patient.
   */
  FamilyMemberHistory_resource_types: resource_types_FamilyMemberHistory,
  /**
   * Prospective warnings of potential issues when providing care to the patient.
   */
  Flag_resource_types: resource_types_Flag,
  /**
   * Describes the intended objective(s) for a patient, group or organization care, for example, weight loss, restoring an activity of daily living, obtaining herd immunity via immunization, meeting a process improvement objective, etc.
   */
  Goal_resource_types: resource_types_Goal,
  /**
   * A formal computable definition of a graph of resources - that is, a coherent set of resources that form a graph by following references. The Graph Definition resource defines a set and makes rules about the set.
   */
  GraphDefinition_resource_types: resource_types_GraphDefinition,
  /**
   * Represents a defined collection of entities that may be discussed or acted upon collectively but which are not expected to act collectively, and are not formally or legally recognized; i.e. a collection of entities that isn't an Organization.
   */
  Group_resource_types: resource_types_Group,
  /**
   * A guidance response is the formal response to a guidance request, including any output parameters returned by the evaluation, as well as the description of any proposed actions to be taken.
   */
  GuidanceResponse_resource_types: resource_types_GuidanceResponse,
  /**
   * The details of a healthcare service available at a location.
   */
  HealthcareService_resource_types: resource_types_HealthcareService,
  /**
   * Representation of the content produced in a DICOM imaging study. A study comprises a set of series, each of which includes a set of Service-Object Pair Instances (SOP Instances - images or other data) acquired or produced in a common context.  A series is of only one modality (e.g. X-ray, CT, MR, ultrasound), but a study may have multiple series of different modalities.
   */
  ImagingStudy_resource_types: resource_types_ImagingStudy,
  /**
   * Describes the event of a patient being administered a vaccine or a record of an immunization as reported by a patient, a clinician or another party.
   */
  Immunization_resource_types: resource_types_Immunization,
  /**
   * Describes a comparison of an immunization event against published recommendations to determine if the administration is "valid" in relation to those  recommendations.
   */
  ImmunizationEvaluation_resource_types: resource_types_ImmunizationEvaluation,
  /**
   * A patient's point-in-time set of recommendations (i.e. forecasting) according to a published schedule with optional supporting justification.
   */
  ImmunizationRecommendation_resource_types: resource_types_ImmunizationRecommendation,
  /**
   * A set of rules of how a particular interoperability or standards problem is solved - typically through the use of FHIR resources. This resource is used to gather all the parts of an implementation guide into a logical whole and to publish a computable definition of all the parts.
   */
  ImplementationGuide_resource_types: resource_types_ImplementationGuide,
  /**
   * Details of a Health Insurance product/plan provided by an organization.
   */
  InsurancePlan_resource_types: resource_types_InsurancePlan,
  /**
   * Invoice containing collected ChargeItems from an Account with calculated individual and total price for Billing purpose.
   */
  Invoice_resource_types: resource_types_Invoice,
  /**
   * The Library resource is a general-purpose container for knowledge asset definitions. It can be used to describe and expose existing knowledge assets such as logic libraries and information model descriptions, as well as to describe a collection of knowledge assets.
   */
  Library_resource_types: resource_types_Library,
  /**
   * Identifies two or more records (resource instances) that refer to the same real-world "occurrence".
   */
  Linkage_resource_types: resource_types_Linkage,
  /**
   * A list is a curated collection of resources.
   */
  List_resource_types: resource_types_List,
  /**
   * Details and position information for a physical place where services are provided and resources and participants may be stored, found, contained, or accommodated.
   */
  Location_resource_types: resource_types_Location,
  /**
   * The Measure resource provides the definition of a quality measure.
   */
  Measure_resource_types: resource_types_Measure,
  /**
   * The MeasureReport resource contains the results of the calculation of a measure; and optionally a reference to the resources involved in that calculation.
   */
  MeasureReport_resource_types: resource_types_MeasureReport,
  /**
   * A photo, video, or audio recording acquired or used in healthcare. The actual content may be inline or provided by direct reference.
   */
  Media_resource_types: resource_types_Media,
  /**
   * This resource is primarily used for the identification and definition of a medication for the purposes of prescribing, dispensing, and administering a medication as well as for making statements about medication use.
   */
  Medication_resource_types: resource_types_Medication,
  /**
   * Describes the event of a patient consuming or otherwise being administered a medication.  This may be as simple as swallowing a tablet or it may be a long running infusion.  Related resources tie this event to the authorizing prescription, and the specific encounter between patient and health care practitioner.
   */
  MedicationAdministration_resource_types: resource_types_MedicationAdministration,
  /**
   * Indicates that a medication product is to be or has been dispensed for a named person/patient.  This includes a description of the medication product (supply) provided and the instructions for administering the medication.  The medication dispense is the result of a pharmacy system responding to a medication order.
   */
  MedicationDispense_resource_types: resource_types_MedicationDispense,
  /**
   * Information about a medication that is used to support knowledge.
   */
  MedicationKnowledge_resource_types: resource_types_MedicationKnowledge,
  /**
   * An order or request for both supply of the medication and the instructions for administration of the medication to a patient. The resource is called "MedicationRequest" rather than "MedicationPrescription" or "MedicationOrder" to generalize the use across inpatient and outpatient settings, including care plans, etc., and to harmonize with workflow patterns.
   */
  MedicationRequest_resource_types: resource_types_MedicationRequest,
  /**
   * A record of a medication that is being consumed by a patient.   A MedicationStatement may indicate that the patient may be taking the medication now or has taken the medication in the past or will be taking the medication in the future.  The source of this information can be the patient, significant other (such as a family member or spouse), or a clinician.  A common scenario where this information is captured is during the history taking process during a patient visit or stay.   The medication information may come from sources such as the patient's memory, from a prescription bottle,  or from a list of medications the patient, clinician or other party maintains.
   * The primary difference between a medication statement and a medication administration is that the medication administration has complete administration information and is based on actual administration information from the person who administered the medication.  A medication statement is often, if not always, less specific.  There is no required date/time when the medication was administered, in fact we only know that a source has reported the patient is taking this medication, where details such as time, quantity, or rate or even medication product may be incomplete or missing or less precise.  As stated earlier, the medication statement information may come from the patient's memory, from a prescription bottle or from a list of medications the patient, clinician or other party maintains.  Medication administration is more formal and is not missing detailed information.
   */
  MedicationStatement_resource_types: resource_types_MedicationStatement,
  /**
   * Detailed definition of a medicinal product, typically for uses other than direct patient care (e.g. regulatory use).
   */
  MedicinalProduct_resource_types: resource_types_MedicinalProduct,
  /**
   * The regulatory authorization of a medicinal product.
   */
  MedicinalProductAuthorization_resource_types: resource_types_MedicinalProductAuthorization,
  /**
   * The clinical particulars - indications, contraindications etc. of a medicinal product, including for regulatory purposes.
   */
  MedicinalProductContraindication_resource_types: resource_types_MedicinalProductContraindication,
  /**
   * Indication for the Medicinal Product.
   */
  MedicinalProductIndication_resource_types: resource_types_MedicinalProductIndication,
  /**
   * An ingredient of a manufactured item or pharmaceutical product.
   */
  MedicinalProductIngredient_resource_types: resource_types_MedicinalProductIngredient,
  /**
   * The interactions of the medicinal product with other medicinal products, or other forms of interactions.
   */
  MedicinalProductInteraction_resource_types: resource_types_MedicinalProductInteraction,
  /**
   * The manufactured item as contained in the packaged medicinal product.
   */
  MedicinalProductManufactured_resource_types: resource_types_MedicinalProductManufactured,
  /**
   * A medicinal product in a container or package.
   */
  MedicinalProductPackaged_resource_types: resource_types_MedicinalProductPackaged,
  /**
   * A pharmaceutical product described in terms of its composition and dose form.
   */
  MedicinalProductPharmaceutical_resource_types: resource_types_MedicinalProductPharmaceutical,
  /**
   * Describe the undesirable effects of the medicinal product.
   */
  MedicinalProductUndesirableEffect_resource_types: resource_types_MedicinalProductUndesirableEffect,
  /**
   * Defines the characteristics of a message that can be shared between systems, including the type of event that initiates the message, the content to be transmitted and what response(s), if any, are permitted.
   */
  MessageDefinition_resource_types: resource_types_MessageDefinition,
  /**
   * The header for a message exchange that is either requesting or responding to an action.  The reference(s) that are the subject of the action as well as other information related to the action are typically transmitted in a bundle in which the MessageHeader resource instance is the first resource in the bundle.
   */
  MessageHeader_resource_types: resource_types_MessageHeader,
  /**
   * Raw data describing a biological sequence.
   */
  MolecularSequence_resource_types: resource_types_MolecularSequence,
  /**
   * A curated namespace that issues unique symbols within that namespace for the identification of concepts, people, devices, etc.  Represents a "System" used within the Identifier and Coding data types.
   */
  NamingSystem_resource_types: resource_types_NamingSystem,
  /**
   * A request to supply a diet, formula feeding (enteral) or oral nutritional supplement to a patient/resident.
   */
  NutritionOrder_resource_types: resource_types_NutritionOrder,
  /**
   * Measurements and simple assertions made about a patient, device or other subject.
   */
  Observation_resource_types: resource_types_Observation,
  /**
   * Set of definitional characteristics for a kind of observation or measurement produced or consumed by an orderable health care service.
   */
  ObservationDefinition_resource_types: resource_types_ObservationDefinition,
  /**
   * A formal computable definition of an operation (on the RESTful interface) or a named query (using the search interaction).
   */
  OperationDefinition_resource_types: resource_types_OperationDefinition,
  /**
   * A collection of error, warning, or information messages that result from a system action.
   */
  OperationOutcome_resource_types: resource_types_OperationOutcome,
  /**
   * A formally or informally recognized grouping of people or organizations formed for the purpose of achieving some form of collective action.  Includes companies, institutions, corporations, departments, community groups, healthcare practice groups, payer/insurer, etc.
   */
  Organization_resource_types: resource_types_Organization,
  /**
   * Defines an affiliation/assotiation/relationship between 2 distinct oganizations, that is not a part-of relationship/sub-division relationship.
   */
  OrganizationAffiliation_resource_types: resource_types_OrganizationAffiliation,
  /**
   * This resource is a non-persisted resource used to pass information into and back from an [operation](operations.html). It has no other use, and there is no RESTful endpoint associated with it.
   */
  Parameters_resource_types: resource_types_Parameters,
  /**
   * Demographics and other administrative information about an individual or animal receiving care or other health-related services.
   */
  Patient_resource_types: resource_types_Patient,
  /**
   * This resource provides the status of the payment for goods and services rendered, and the request and response resource references.
   */
  PaymentNotice_resource_types: resource_types_PaymentNotice,
  /**
   * This resource provides the details including amount of a payment and allocates the payment items being paid.
   */
  PaymentReconciliation_resource_types: resource_types_PaymentReconciliation,
  /**
   * Demographics and administrative information about a person independent of a specific health-related context.
   */
  Person_resource_types: resource_types_Person,
  /**
   * This resource allows for the definition of various types of plans as a sharable, consumable, and executable artifact. The resource is general enough to support the description of a broad range of clinical artifacts such as clinical decision support rules, order sets and protocols.
   */
  PlanDefinition_resource_types: resource_types_PlanDefinition,
  /**
   * A person who is directly or indirectly involved in the provisioning of healthcare.
   */
  Practitioner_resource_types: resource_types_Practitioner,
  /**
   * A specific set of Roles/Locations/specialties/services that a practitioner may perform at an organization for a period of time.
   */
  PractitionerRole_resource_types: resource_types_PractitionerRole,
  /**
   * An action that is or was performed on or for a patient. This can be a physical intervention like an operation, or less invasive like long term services, counseling, or hypnotherapy.
   */
  Procedure_resource_types: resource_types_Procedure,
  /**
   * Provenance of a resource is a record that describes entities and processes involved in producing and delivering or otherwise influencing that resource. Provenance provides a critical foundation for assessing authenticity, enabling trust, and allowing reproducibility. Provenance assertions are a form of contextual metadata and can themselves become important records with their own provenance. Provenance statement indicates clinical significance in terms of confidence in authenticity, reliability, and trustworthiness, integrity, and stage in lifecycle (e.g. Document Completion - has the artifact been legally authenticated), all of which may impact security, privacy, and trust policies.
   */
  Provenance_resource_types: resource_types_Provenance,
  /**
   * A structured set of questions intended to guide the collection of answers from end-users. Questionnaires provide detailed control over order, presentation, phraseology and grouping to allow coherent, consistent data collection.
   */
  Questionnaire_resource_types: resource_types_Questionnaire,
  /**
   * A structured set of questions and their answers. The questions are ordered and grouped into coherent subsets, corresponding to the structure of the grouping of the questionnaire being responded to.
   */
  QuestionnaireResponse_resource_types: resource_types_QuestionnaireResponse,
  /**
   * Information about a person that is involved in the care for a patient, but who is not the target of healthcare, nor has a formal responsibility in the care process.
   */
  RelatedPerson_resource_types: resource_types_RelatedPerson,
  /**
   * A group of related requests that can be used to capture intended activities that have inter-dependencies such as "give this medication after that one".
   */
  RequestGroup_resource_types: resource_types_RequestGroup,
  /**
   * The ResearchDefinition resource describes the conditional state (population and any exposures being compared within the population) and outcome (if specified) that the knowledge (evidence, assertion, recommendation) is about.
   */
  ResearchDefinition_resource_types: resource_types_ResearchDefinition,
  /**
   * The ResearchElementDefinition resource describes a "PICO" element that knowledge (evidence, assertion, recommendation) is about.
   */
  ResearchElementDefinition_resource_types: resource_types_ResearchElementDefinition,
  /**
   * A process where a researcher or organization plans and then executes a series of steps intended to increase the field of healthcare-related knowledge.  This includes studies of safety, efficacy, comparative effectiveness and other information about medications, devices, therapies and other interventional and investigative techniques.  A ResearchStudy involves the gathering of information about human or animal subjects.
   */
  ResearchStudy_resource_types: resource_types_ResearchStudy,
  /**
   * A physical entity which is the primary unit of operational and/or administrative interest in a study.
   */
  ResearchSubject_resource_types: resource_types_ResearchSubject,
  /**
   * This is the base resource type for everything.
   */
  Resource_resource_types: resource_types_Resource,
  /**
   * An assessment of the likely outcome(s) for a patient or other subject as well as the likelihood of each outcome.
   */
  RiskAssessment_resource_types: resource_types_RiskAssessment,
  /**
   * The RiskEvidenceSynthesis resource describes the likelihood of an outcome in a population plus exposure state where the risk estimate is derived from a combination of research studies.
   */
  RiskEvidenceSynthesis_resource_types: resource_types_RiskEvidenceSynthesis,
  /**
   * A container for slots of time that may be available for booking appointments.
   */
  Schedule_resource_types: resource_types_Schedule,
  /**
   * A search parameter that defines a named search item that can be used to search/filter on a resource.
   */
  SearchParameter_resource_types: resource_types_SearchParameter,
  /**
   * A record of a request for service such as diagnostic investigations, treatments, or operations to be performed.
   */
  ServiceRequest_resource_types: resource_types_ServiceRequest,
  /**
   * A slot of time on a schedule that may be available for booking appointments.
   */
  Slot_resource_types: resource_types_Slot,
  /**
   * A sample to be used for analysis.
   */
  Specimen_resource_types: resource_types_Specimen,
  /**
   * A kind of specimen with associated set of requirements.
   */
  SpecimenDefinition_resource_types: resource_types_SpecimenDefinition,
  /**
   * A definition of a FHIR structure. This resource is used to describe the underlying resources, data types defined in FHIR, and also for describing extensions and constraints on resources and data types.
   */
  StructureDefinition_resource_types: resource_types_StructureDefinition,
  /**
   * A Map of relationships between 2 structures that can be used to transform data.
   */
  StructureMap_resource_types: resource_types_StructureMap,
  /**
   * The subscription resource is used to define a push-based subscription from a server to another system. Once a subscription is registered with the server, the server checks every resource that is created or updated, and if the resource matches the given criteria, it sends a message on the defined "channel" so that another system can take an appropriate action.
   */
  Subscription_resource_types: resource_types_Subscription,
  /**
   * A homogeneous material with a definite composition.
   */
  Substance_resource_types: resource_types_Substance,
  /**
   * Nucleic acids are defined by three distinct elements: the base, sugar and linkage. Individual substance/moiety IDs will be created for each of these elements. The nucleotide sequence will be always entered in the 5’-3’ direction.
   */
  SubstanceNucleicAcid_resource_types: resource_types_SubstanceNucleicAcid,
  /**
   * Todo.
   */
  SubstancePolymer_resource_types: resource_types_SubstancePolymer,
  /**
   * A SubstanceProtein is defined as a single unit of a linear amino acid sequence, or a combination of subunits that are either covalently linked or have a defined invariant stoichiometric relationship. This includes all synthetic, recombinant and purified SubstanceProteins of defined sequence, whether the use is therapeutic or prophylactic. This set of elements will be used to describe albumins, coagulation factors, cytokines, growth factors, peptide/SubstanceProtein hormones, enzymes, toxins, toxoids, recombinant vaccines, and immunomodulators.
   */
  SubstanceProtein_resource_types: resource_types_SubstanceProtein,
  /**
   * Todo.
   */
  SubstanceReferenceInformation_resource_types: resource_types_SubstanceReferenceInformation,
  /**
   * Source material shall capture information on the taxonomic and anatomical origins as well as the fraction of a material that can result in or can be modified to form a substance. This set of data elements shall be used to define polymer substances isolated from biological matrices. Taxonomic and anatomical origins shall be described using a controlled vocabulary as required. This information is captured for naturally derived polymers ( . starch) and structurally diverse substances. For Organisms belonging to the Kingdom Plantae the Substance level defines the fresh material of a single species or infraspecies, the Herbal Drug and the Herbal preparation. For Herbal preparations, the fraction information will be captured at the Substance information level and additional information for herbal extracts will be captured at the Specified Substance Group 1 information level. See for further explanation the Substance Class: Structurally Diverse and the herbal annex.
   */
  SubstanceSourceMaterial_resource_types: resource_types_SubstanceSourceMaterial,
  /**
   * The detailed description of a substance, typically at a level beyond what is used for prescribing.
   */
  SubstanceSpecification_resource_types: resource_types_SubstanceSpecification,
  /**
   * Record of delivery of what is supplied.
   */
  SupplyDelivery_resource_types: resource_types_SupplyDelivery,
  /**
   * A record of a request for a medication, substance or device used in the healthcare setting.
   */
  SupplyRequest_resource_types: resource_types_SupplyRequest,
  /**
   * A task to be performed.
   */
  Task_resource_types: resource_types_Task,
  /**
   * A TerminologyCapabilities resource documents a set of capabilities (behaviors) of a FHIR Terminology Server that may be used as a statement of actual server functionality or a statement of required or desired server implementation.
   */
  TerminologyCapabilities_resource_types: resource_types_TerminologyCapabilities,
  /**
   * A summary of information based on the results of executing a TestScript.
   */
  TestReport_resource_types: resource_types_TestReport,
  /**
   * A structured set of tests against a FHIR server or client implementation to determine compliance against the FHIR specification.
   */
  TestScript_resource_types: resource_types_TestScript,
  /**
   * A ValueSet resource instance specifies a set of codes drawn from one or more code systems, intended for use in a particular context. Value sets link between [[[CodeSystem]]] definitions and their use in [coded elements](terminologies.html).
   */
  ValueSet_resource_types: resource_types_ValueSet,
  /**
   * Describes validation requirements, source(s), status and dates for one or more elements.
   */
  VerificationResult_resource_types: resource_types_VerificationResult,
  /**
   * An authorization for the provision of glasses and/or contact lenses to a patient.
   */
  VisionPrescription_resource_types: resource_types_VisionPrescription,
};
const audit_event_action_Create: Coding = {
  code: 'C',
  display: 'Create',
  system: 'http://hl7.org/fhir/audit-event-action',
};
const audit_event_action_Delete: Coding = {
  code: 'D',
  display: 'Delete',
  system: 'http://hl7.org/fhir/audit-event-action',
};
const audit_event_action_Execute: Coding = {
  code: 'E',
  display: 'Execute',
  system: 'http://hl7.org/fhir/audit-event-action',
};
const audit_event_action_ReadViewPrint: Coding = {
  code: 'R',
  display: 'Read/View/Print',
  system: 'http://hl7.org/fhir/audit-event-action',
};
const audit_event_action_Update: Coding = {
  code: 'U',
  display: 'Update',
  system: 'http://hl7.org/fhir/audit-event-action',
};
/**
 * Indicator for type of action performed during the event that generated the event.
 */
export const AuditEventAction = {
  /**
   * Create a new database object, such as placing an order.
   */
  Create: audit_event_action_Create,
  /**
   * Delete items, such as a doctor master file record.
   */
  Delete: audit_event_action_Delete,
  /**
   * Perform a system or application function such as log-on, program execution or use of an object's method, or perform a query/search operation.
   */
  Execute: audit_event_action_Execute,
  /**
   * Display or print data, such as a doctor census.
   */
  ReadViewPrint: audit_event_action_ReadViewPrint,
  /**
   * Update data, such as revise patient information.
   */
  Update: audit_event_action_Update,
};
const audit_event_outcome_Success: Coding = {
  code: '0',
  display: 'Success',
  system: 'http://hl7.org/fhir/audit-event-outcome',
};
const audit_event_outcome_MajorFailure: Coding = {
  code: '12',
  display: 'Major failure',
  system: 'http://hl7.org/fhir/audit-event-outcome',
};
const audit_event_outcome_MinorFailure: Coding = {
  code: '4',
  display: 'Minor failure',
  system: 'http://hl7.org/fhir/audit-event-outcome',
};
const audit_event_outcome_SeriousFailure: Coding = {
  code: '8',
  display: 'Serious failure',
  system: 'http://hl7.org/fhir/audit-event-outcome',
};
/**
 * Indicates whether the event succeeded or failed.
 */
export const AuditEventOutcome = {
  /**
   * The operation completed successfully (whether with warnings or not).
   */
  Success: audit_event_outcome_Success,
  /**
   * An error of such magnitude occurred that the system is no longer available for use (i.e. the system died).
   */
  MajorFailure: audit_event_outcome_MajorFailure,
  /**
   * The action was not successful due to some kind of minor failure (often equivalent to an HTTP 400 response).
   */
  MinorFailure: audit_event_outcome_MinorFailure,
  /**
   * The action was not successful due to some kind of unexpected error (often equivalent to an HTTP 500 response).
   */
  SeriousFailure: audit_event_outcome_SeriousFailure,
};
const dicom_dcim_ApplicationStart: Coding = {
  code: '110120',
  display: 'Application Start',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_ApplicationStop: Coding = {
  code: '110121',
  display: 'Application Stop',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_Login: Coding = {
  code: '110122',
  display: 'Login',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_Logout: Coding = {
  code: '110123',
  display: 'Logout',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_Attach: Coding = {
  code: '110124',
  display: 'Attach',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_Detach: Coding = {
  code: '110125',
  display: 'Detach',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_NodeAuthentication: Coding = {
  code: '110126',
  display: 'Node Authentication',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_EmergencyOverrideStarted: Coding = {
  code: '110127',
  display: 'Emergency Override Started',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_NetworkConfiguration: Coding = {
  code: '110128',
  display: 'Network Configuration',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_SecurityConfiguration: Coding = {
  code: '110129',
  display: 'Security Configuration',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_HardwareConfiguration: Coding = {
  code: '110130',
  display: 'Hardware Configuration',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_SoftwareConfiguration: Coding = {
  code: '110131',
  display: 'Software Configuration',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_UseOfRestrictedFunction: Coding = {
  code: '110132',
  display: 'Use of Restricted Function',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_AuditRecordingStopped: Coding = {
  code: '110133',
  display: 'Audit Recording Stopped',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_AuditRecordingStarted: Coding = {
  code: '110134',
  display: 'Audit Recording Started',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_ObjectSecurityAttributesChanged: Coding = {
  code: '110135',
  display: 'Object Security Attributes Changed',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_SecurityRolesChanged: Coding = {
  code: '110136',
  display: 'Security Roles Changed',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_UserSecurityAttributesChanged: Coding = {
  code: '110137',
  display: 'User security Attributes Changed',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_EmergencyOverrideStopped: Coding = {
  code: '110138',
  display: 'Emergency Override Stopped',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_RemoteServiceOperationStarted: Coding = {
  code: '110139',
  display: 'Remote Service Operation Started',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_RemoteServiceOperationStopped: Coding = {
  code: '110140',
  display: 'Remote Service Operation Stopped',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_LocalServiceOperationStarted: Coding = {
  code: '110141',
  display: 'Local Service Operation Started',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_LocalServiceOperationStopped: Coding = {
  code: '110142',
  display: 'Local Service Operation Stopped',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const restful_interaction_Batch: Coding = {
  code: 'batch',
  display: 'batch',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_Capabilities: Coding = {
  code: 'capabilities',
  display: 'capabilities',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_Create: Coding = {
  code: 'create',
  display: 'create',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_Delete: Coding = {
  code: 'delete',
  display: 'delete',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_History: Coding = {
  code: 'history',
  display: 'history',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_HistoryInstance: Coding = {
  code: 'history-instance',
  display: 'history-instance',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_HistorySystem: Coding = {
  code: 'history-system',
  display: 'history-system',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_HistoryType: Coding = {
  code: 'history-type',
  display: 'history-type',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_Operation: Coding = {
  code: 'operation',
  display: 'operation',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_Patch: Coding = {
  code: 'patch',
  display: 'patch',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_Read: Coding = {
  code: 'read',
  display: 'read',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_Search: Coding = {
  code: 'search',
  display: 'search',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_SearchSystem: Coding = {
  code: 'search-system',
  display: 'search-system',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_SearchType: Coding = {
  code: 'search-type',
  display: 'search-type',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_Transaction: Coding = {
  code: 'transaction',
  display: 'transaction',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_Update: Coding = {
  code: 'update',
  display: 'update',
  system: 'http://hl7.org/fhir/restful-interaction',
};
const restful_interaction_Vread: Coding = {
  code: 'vread',
  display: 'vread',
  system: 'http://hl7.org/fhir/restful-interaction',
};
/**
 * More detailed code concerning the type of the audit event - defined by DICOM with some FHIR specific additions.
 */
export const AuditEventSubType = {
  /**
   * Audit event: Application Entity has started
   */
  ApplicationStart_dicom_dcim: dicom_dcim_ApplicationStart,
  /**
   * Audit event: Application Entity has stopped
   */
  ApplicationStop_dicom_dcim: dicom_dcim_ApplicationStop,
  /**
   * Audit event: User login has been attempted
   */
  Login_dicom_dcim: dicom_dcim_Login,
  /**
   * Audit event: User logout has been attempted
   */
  Logout_dicom_dcim: dicom_dcim_Logout,
  /**
   * Audit event: Node has been attached
   */
  Attach_dicom_dcim: dicom_dcim_Attach,
  /**
   * Audit event: Node has been detached
   */
  Detach_dicom_dcim: dicom_dcim_Detach,
  /**
   * Audit event: Node Authentication has been attempted
   */
  NodeAuthentication_dicom_dcim: dicom_dcim_NodeAuthentication,
  /**
   * Audit event: Emergency Override has started
   */
  EmergencyOverrideStarted_dicom_dcim: dicom_dcim_EmergencyOverrideStarted,
  /**
   * Audit event: Network configuration has been changed
   */
  NetworkConfiguration_dicom_dcim: dicom_dcim_NetworkConfiguration,
  /**
   * Audit event: Security configuration has been changed
   */
  SecurityConfiguration_dicom_dcim: dicom_dcim_SecurityConfiguration,
  /**
   * Audit event: Hardware configuration has been changed
   */
  HardwareConfiguration_dicom_dcim: dicom_dcim_HardwareConfiguration,
  /**
   * Audit event: Software configuration has been changed
   */
  SoftwareConfiguration_dicom_dcim: dicom_dcim_SoftwareConfiguration,
  /**
   * Audit event: A use of a restricted function has been attempted
   */
  UseOfRestrictedFunction_dicom_dcim: dicom_dcim_UseOfRestrictedFunction,
  /**
   * Audit event: Audit recording has been stopped
   */
  AuditRecordingStopped_dicom_dcim: dicom_dcim_AuditRecordingStopped,
  /**
   * Audit event: Audit recording has been started
   */
  AuditRecordingStarted_dicom_dcim: dicom_dcim_AuditRecordingStarted,
  /**
   * Audit event: Security attributes of an object have been changed
   */
  ObjectSecurityAttributesChanged_dicom_dcim: dicom_dcim_ObjectSecurityAttributesChanged,
  /**
   * Audit event: Security roles have been changed
   */
  SecurityRolesChanged_dicom_dcim: dicom_dcim_SecurityRolesChanged,
  /**
   * Audit event: Security attributes of a user have been changed
   */
  UserSecurityAttributesChanged_dicom_dcim: dicom_dcim_UserSecurityAttributesChanged,
  /**
   * Audit event: Emergency Override has Stopped
   */
  EmergencyOverrideStopped_dicom_dcim: dicom_dcim_EmergencyOverrideStopped,
  /**
   * Audit event: Remote Service Operation has Begun
   */
  RemoteServiceOperationStarted_dicom_dcim: dicom_dcim_RemoteServiceOperationStarted,
  /**
   * Audit event: Remote Service Operation has Stopped
   */
  RemoteServiceOperationStopped_dicom_dcim: dicom_dcim_RemoteServiceOperationStopped,
  /**
   * Audit event: Local Service Operation has Begun
   */
  LocalServiceOperationStarted_dicom_dcim: dicom_dcim_LocalServiceOperationStarted,
  /**
   * Audit event: Local Service Operation Stopped
   */
  LocalServiceOperationStopped_dicom_dcim: dicom_dcim_LocalServiceOperationStopped,
  /**
   * perform a set of a separate interactions in a single http operation
   */
  Batch_restful_interaction: restful_interaction_Batch,
  /**
   * Get a Capability Statement for the system.
   */
  Capabilities_restful_interaction: restful_interaction_Capabilities,
  /**
   * Create a new resource with a server assigned id.
   */
  Create_restful_interaction: restful_interaction_Create,
  /**
   * Delete a resource.
   */
  Delete_restful_interaction: restful_interaction_Delete,
  /**
   * Retrieve the change history for a particular resource, type of resource, or the entire system.
   */
  History_restful_interaction: restful_interaction_History,
  /**
   * Retrieve the change history for a particular resource.
   */
  HistoryInstance_restful_interaction: restful_interaction_HistoryInstance,
  /**
   * Retrieve the change history for all resources on a system.
   */
  HistorySystem_restful_interaction: restful_interaction_HistorySystem,
  /**
   * Retrieve the change history for all resources of a particular type.
   */
  HistoryType_restful_interaction: restful_interaction_HistoryType,
  /**
   * Perform an operation as defined by an OperationDefinition.
   */
  Operation_restful_interaction: restful_interaction_Operation,
  /**
   * Update an existing resource by posting a set of changes to it.
   */
  Patch_restful_interaction: restful_interaction_Patch,
  /**
   * Read the current state of the resource.
   */
  Read_restful_interaction: restful_interaction_Read,
  /**
   * Search a resource type or all resources based on some filter criteria.
   */
  Search_restful_interaction: restful_interaction_Search,
  /**
   * Search all resources based on some filter criteria.
   */
  SearchSystem_restful_interaction: restful_interaction_SearchSystem,
  /**
   * Search all resources of the specified type based on some filter criteria.
   */
  SearchType_restful_interaction: restful_interaction_SearchType,
  /**
   * Update, create or delete a set of resources as a single transaction.
   */
  Transaction_restful_interaction: restful_interaction_Transaction,
  /**
   * Update an existing resource by its id (or create it if it is new).
   */
  Update_restful_interaction: restful_interaction_Update,
  /**
   * Read the state of a specific version of the resource.
   */
  Vread_restful_interaction: restful_interaction_Vread,
};
const dicom_dcim_ApplicationActivity: Coding = {
  code: '110100',
  display: 'Application Activity',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_AuditLogUsed: Coding = {
  code: '110101',
  display: 'Audit Log Used',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_BeginTransferringDICOMInstances: Coding = {
  code: '110102',
  display: 'Begin Transferring DICOM Instances',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_DICOMInstancesAccessed: Coding = {
  code: '110103',
  display: 'DICOM Instances Accessed',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_DICOMInstancesTransferred: Coding = {
  code: '110104',
  display: 'DICOM Instances Transferred',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_DICOMStudyDeleted: Coding = {
  code: '110105',
  display: 'DICOM Study Deleted',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_Export: Coding = {
  code: '110106',
  display: 'Export',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_Import: Coding = {
  code: '110107',
  display: 'Import',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_NetworkEntry: Coding = {
  code: '110108',
  display: 'Network Entry',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_OrderRecord: Coding = {
  code: '110109',
  display: 'Order Record',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_PatientRecord: Coding = {
  code: '110110',
  display: 'Patient Record',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_ProcedureRecord: Coding = {
  code: '110111',
  display: 'Procedure Record',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_Query: Coding = {
  code: '110112',
  display: 'Query',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_SecurityAlert: Coding = {
  code: '110113',
  display: 'Security Alert',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_UserAuthentication: Coding = {
  code: '110114',
  display: 'User Authentication',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const iso_21089_lifecycle_AccessViewRecordLifecycleEvent: Coding = {
  code: 'access',
  display: 'Access/View Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_AmendUpdateRecordLifecycleEvent: Coding = {
  code: 'amend',
  display: 'Amend (Update) Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_ArchiveRecordLifecycleEvent: Coding = {
  code: 'archive',
  display: 'Archive Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_AttestRecordLifecycleEvent: Coding = {
  code: 'attest',
  display: 'Attest Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_DecryptRecordLifecycleEvent: Coding = {
  code: 'decrypt',
  display: 'Decrypt Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_DeIdentifyAnononymizeRecordLifecycleEvent: Coding = {
  code: 'deidentify',
  display: 'De-Identify (Anononymize) Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_DeprecateRecordLifecycleEvent: Coding = {
  code: 'deprecate',
  display: 'Deprecate Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_DestroyDeleteRecordLifecycleEvent: Coding = {
  code: 'destroy',
  display: 'Destroy/Delete Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_DiscloseRecordLifecycleEvent: Coding = {
  code: 'disclose',
  display: 'Disclose Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_EncryptRecordLifecycleEvent: Coding = {
  code: 'encrypt',
  display: 'Encrypt Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_ExtractRecordLifecycleEvent: Coding = {
  code: 'extract',
  display: 'Extract Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_AddLegalHoldRecordLifecycleEvent: Coding = {
  code: 'hold',
  display: 'Add Legal Hold Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_LinkRecordLifecycleEvent: Coding = {
  code: 'link',
  display: 'Link Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_MergeRecordLifecycleEvent: Coding = {
  code: 'merge',
  display: 'Merge Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_OriginateRetainRecordLifecycleEvent: Coding = {
  code: 'originate',
  display: 'Originate/Retain Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_PseudonymizeRecordLifecycleEvent: Coding = {
  code: 'pseudonymize',
  display: 'Pseudonymize Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_ReActivateRecordLifecycleEvent: Coding = {
  code: 'reactivate',
  display: 'Re-activate Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_ReceiveRetainRecordLifecycleEvent: Coding = {
  code: 'receive',
  display: 'Receive/Retain Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_ReIdentifyRecordLifecycleEvent: Coding = {
  code: 'reidentify',
  display: 'Re-identify Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_ReportOutputRecordLifecycleEvent: Coding = {
  code: 'report',
  display: 'Report (Output) Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const audit_event_type_RESTfulOperation: Coding = {
  code: 'rest',
  display: 'RESTful Operation',
  system: 'http://terminology.hl7.org/CodeSystem/audit-event-type',
};
const iso_21089_lifecycle_RestoreRecordLifecycleEvent: Coding = {
  code: 'restore',
  display: 'Restore Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_TransformTranslateRecordLifecycleEvent: Coding = {
  code: 'transform',
  display: 'Transform/Translate Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_TransmitRecordLifecycleEvent: Coding = {
  code: 'transmit',
  display: 'Transmit Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_RemoveLegalHoldRecordLifecycleEvent: Coding = {
  code: 'unhold',
  display: 'Remove Legal Hold Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_UnlinkRecordLifecycleEvent: Coding = {
  code: 'unlink',
  display: 'Unlink Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_UnmergeRecordLifecycleEvent: Coding = {
  code: 'unmerge',
  display: 'Unmerge Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
const iso_21089_lifecycle_VerifyRecordLifecycleEvent: Coding = {
  code: 'verify',
  display: 'Verify Record Lifecycle Event',
  system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
};
/**
 * Event Types for Audit Events - defined by DICOM with some FHIR specific additions.
 */
export const AuditEventType = {
  /**
   * Audit event: Application Activity has taken place
   */
  ApplicationActivity_dicom_dcim: dicom_dcim_ApplicationActivity,
  /**
   * Audit event: Audit Log has been used
   */
  AuditLogUsed_dicom_dcim: dicom_dcim_AuditLogUsed,
  /**
   * Audit event: Storage of DICOM Instances has begun
   */
  BeginTransferringDICOMInstances_dicom_dcim: dicom_dcim_BeginTransferringDICOMInstances,
  /**
   * Audit event: DICOM Instances have been created, read, updated, or deleted
   */
  DICOMInstancesAccessed_dicom_dcim: dicom_dcim_DICOMInstancesAccessed,
  /**
   * Audit event: Storage of DICOM Instances has been completed
   */
  DICOMInstancesTransferred_dicom_dcim: dicom_dcim_DICOMInstancesTransferred,
  /**
   * Audit event: Entire Study has been deleted
   */
  DICOMStudyDeleted_dicom_dcim: dicom_dcim_DICOMStudyDeleted,
  /**
   * Audit event: Data has been exported out of the system
   */
  Export_dicom_dcim: dicom_dcim_Export,
  /**
   * Audit event: Data has been imported into the system
   */
  Import_dicom_dcim: dicom_dcim_Import,
  /**
   * Audit event: System has joined or left network
   */
  NetworkEntry_dicom_dcim: dicom_dcim_NetworkEntry,
  /**
   * Audit event: Order has been created, read, updated or deleted
   */
  OrderRecord_dicom_dcim: dicom_dcim_OrderRecord,
  /**
   * Audit event: Patient Record has been created, read, updated, or deleted
   */
  PatientRecord_dicom_dcim: dicom_dcim_PatientRecord,
  /**
   * Audit event: Procedure Record has been created, read, updated, or deleted
   */
  ProcedureRecord_dicom_dcim: dicom_dcim_ProcedureRecord,
  /**
   * Audit event: Query has been made
   */
  Query_dicom_dcim: dicom_dcim_Query,
  /**
   * Audit event: Security Alert has been raised
   */
  SecurityAlert_dicom_dcim: dicom_dcim_SecurityAlert,
  /**
   * Audit event: User Authentication has been attempted
   */
  UserAuthentication_dicom_dcim: dicom_dcim_UserAuthentication,
  /**
   * Occurs when an agent causes the system to obtain and open a record entry for inspection or review.
   */
  AccessViewRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_AccessViewRecordLifecycleEvent,
  /**
   * Occurs when an agent makes any change to record entry content currently residing in storage considered permanent (persistent).
   */
  AmendUpdateRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_AmendUpdateRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to create and move archive artifacts containing record entry content, typically to long-term offline storage.
   */
  ArchiveRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_ArchiveRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to capture the agent’s digital signature (or equivalent indication) during formal validation of record entry content.
   */
  AttestRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_AttestRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to decode record entry content from a cipher.
   */
  DecryptRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_DecryptRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to scrub record entry content to reduce the association between a set of identifying data and the data subject in a way that might or might not be reversible.
   */
  DeIdentifyAnononymizeRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_DeIdentifyAnononymizeRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to tag record entry(ies) as obsolete, erroneous or untrustworthy, to warn against its future use.
   */
  DeprecateRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_DeprecateRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to permanently erase record entry content from the system.
   */
  DestroyDeleteRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_DestroyDeleteRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to release, transfer, provision access to, or otherwise divulge record entry content.
   */
  DiscloseRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_DiscloseRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to encode record entry content in a cipher.
   */
  EncryptRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_EncryptRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to selectively pull out a subset of record entry content, based on explicit criteria.
   */
  ExtractRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_ExtractRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to tag or otherwise indicate special access management and suspension of record entry deletion/destruction, if deemed relevant to a lawsuit or which are reasonably anticipated to be relevant or to fulfill organizational policy under the legal doctrine of “duty to preserve”.
   */
  AddLegalHoldRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_AddLegalHoldRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to connect related record entries.
   */
  LinkRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_LinkRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to combine or join content from two or more record entries, resulting in a single logical record entry.
   */
  MergeRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_MergeRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to: a) initiate capture of potential record content, and b) incorporate that content into the storage considered a permanent part of the health record.
   */
  OriginateRetainRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_OriginateRetainRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to remove record entry content to reduce the association between a set of identifying data and the data subject in a way that may be reversible.
   */
  PseudonymizeRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_PseudonymizeRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to recreate or restore full status to record entries previously deleted or deprecated.
   */
  ReActivateRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_ReActivateRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to a) initiate capture of data content from elsewhere, and b) incorporate that content into the storage considered a permanent part of the health record.
   */
  ReceiveRetainRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_ReceiveRetainRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to restore information to data that allows identification of information source and/or information subject.
   */
  ReIdentifyRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_ReIdentifyRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to produce and deliver record entry content in a particular form and manner.
   */
  ReportOutputRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_ReportOutputRecordLifecycleEvent,
  /**
   * Audit Event: Execution of a RESTful operation as defined by FHIR.
   */
  RESTfulOperation_audit_event_type: audit_event_type_RESTfulOperation,
  /**
   * Occurs when an agent causes the system to recreate record entries and their content from a previous created archive artefact.
   */
  RestoreRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_RestoreRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to change the form, language or code system used to represent record entry content.
   */
  TransformTranslateRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_TransformTranslateRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to send record entry content from one (EHR/PHR/other) system to another.
   */
  TransmitRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_TransmitRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to remove a tag or other cues for special access management had required to fulfill organizational policy under the legal doctrine of “duty to preserve”.
   */
  RemoveLegalHoldRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_RemoveLegalHoldRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to disconnect two or more record entries previously connected, rendering them separate (disconnected) again.
   */
  UnlinkRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_UnlinkRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to reverse a previous record entry merge operation, rendering them separate again.
   */
  UnmergeRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_UnmergeRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to confirm compliance of data or data objects with regulations, requirements, specifications, or other imposed conditions based on organizational policy.
   */
  VerifyRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_VerifyRecordLifecycleEvent,
};
const audit_source_type_UserDevice: Coding = {
  code: '1',
  display: 'User Device',
  system: 'http://terminology.hl7.org/CodeSystem/security-source-type',
};
const audit_source_type_DataInterface: Coding = {
  code: '2',
  display: 'Data Interface',
  system: 'http://terminology.hl7.org/CodeSystem/security-source-type',
};
const audit_source_type_WebServer: Coding = {
  code: '3',
  display: 'Web Server',
  system: 'http://terminology.hl7.org/CodeSystem/security-source-type',
};
const audit_source_type_ApplicationServer: Coding = {
  code: '4',
  display: 'Application Server',
  system: 'http://terminology.hl7.org/CodeSystem/security-source-type',
};
const audit_source_type_DatabaseServer: Coding = {
  code: '5',
  display: 'Database Server',
  system: 'http://terminology.hl7.org/CodeSystem/security-source-type',
};
const audit_source_type_SecurityServer: Coding = {
  code: '6',
  display: 'Security Server',
  system: 'http://terminology.hl7.org/CodeSystem/security-source-type',
};
const audit_source_type_NetworkDevice: Coding = {
  code: '7',
  display: 'Network Device',
  system: 'http://terminology.hl7.org/CodeSystem/security-source-type',
};
const audit_source_type_NetworkRouter: Coding = {
  code: '8',
  display: 'Network Router',
  system: 'http://terminology.hl7.org/CodeSystem/security-source-type',
};
const audit_source_type_Other: Coding = {
  code: '9',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/security-source-type',
};
/**
 * The type of process where the audit event originated from.
 */
export const AuditSourceType = {
  /**
   * End-user display device, diagnostic device.
   */
  UserDevice: audit_source_type_UserDevice,
  /**
   * Data acquisition device or instrument.
   */
  DataInterface: audit_source_type_DataInterface,
  /**
   * Web Server process or thread.
   */
  WebServer: audit_source_type_WebServer,
  /**
   * Application Server process or thread.
   */
  ApplicationServer: audit_source_type_ApplicationServer,
  /**
   * Database Server process or thread.
   */
  DatabaseServer: audit_source_type_DatabaseServer,
  /**
   * Security server, e.g. a domain controller.
   */
  SecurityServer: audit_source_type_SecurityServer,
  /**
   * ISO level 1-3 network component.
   */
  NetworkDevice: audit_source_type_NetworkDevice,
  /**
   * ISO level 4-6 operating software.
   */
  NetworkRouter: audit_source_type_NetworkRouter,
  /**
   * Other kind of device (defined by DICOM, but some other code/system can be used).
   */
  Other: audit_source_type_Other,
};
const basic_resource_type_Account: Coding = {
  code: 'account',
  display: 'Account',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_InvoiceAdjudication: Coding = {
  code: 'adjudicat',
  display: 'Invoice Adjudication',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_AdministrativeActivity: Coding = {
  code: 'adminact',
  display: 'Administrative Activity',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_AdverseEvent: Coding = {
  code: 'advevent',
  display: 'Adverse Event',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_AppointmentRequest: Coding = {
  code: 'aptmtreq',
  display: 'Appointment Request',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_Consent: Coding = {
  code: 'consent',
  display: 'Consent',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_Diet: Coding = {
  code: 'diet',
  display: 'Diet',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_Exposure: Coding = {
  code: 'exposure',
  display: 'Exposure',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_Investigation: Coding = {
  code: 'investigation',
  display: 'Investigation',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_Invoice: Coding = {
  code: 'invoice',
  display: 'Invoice',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_Predetermination: Coding = {
  code: 'predetermine',
  display: 'Predetermination',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_PreDeterminationRequest: Coding = {
  code: 'predetreq',
  display: 'Pre-determination Request',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_Protocol: Coding = {
  code: 'protocol',
  display: 'Protocol',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_Referral: Coding = {
  code: 'referral',
  display: 'Referral',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_Study: Coding = {
  code: 'study',
  display: 'Study',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
const basic_resource_type_Transfer: Coding = {
  code: 'transfer',
  display: 'Transfer',
  system: 'http://terminology.hl7.org/CodeSystem/basic-resource-type',
};
/**
 * This value set defines codes for resources not yet supported by (or which will never be supported by) FHIR.  Many of the codes listed here will eventually be turned into official resources.  However, there is no guarantee that any particular resource will be created nor that the scope will be exactly as defined by the codes presented here.  Codes in this set will be deprecated if/when formal resources are defined that encompass these concepts.
 */
export const BasicResourceType = {
  /**
   * A financial instrument used to track costs, charges or other amounts.
   */
  Account: basic_resource_type_Account,
  /**
   * The determination of what will be paid against a particular invoice based on coverage, plan rules, etc.
   */
  InvoiceAdjudication: basic_resource_type_InvoiceAdjudication,
  /**
   * An occurrence of a non-care-related event in the healthcare domain, such as approvals, reviews, etc.
   */
  AdministrativeActivity: basic_resource_type_AdministrativeActivity,
  /**
   * An undesired reaction caused by exposure to some agent (e.g. a medication, immunization, food, or environmental agent).
   */
  AdverseEvent: basic_resource_type_AdverseEvent,
  /**
   * A request that a time be scheduled for a type of service for a specified patient, potentially subject to other constraints
   */
  AppointmentRequest: basic_resource_type_AppointmentRequest,
  /**
   * An assertion of permission for an activity or set of activities to occur, possibly subject to particular limitations; e.g. surgical consent, information disclosure consent, etc.
   */
  Consent: basic_resource_type_Consent,
  /**
   * The specification of a set of food and/or other nutritional material to be delivered to a patient.
   */
  Diet: basic_resource_type_Diet,
  /**
   * Record of a situation where a subject was exposed to a substance.  Usually of interest to public health.
   */
  Exposure: basic_resource_type_Exposure,
  /**
   * A formalized inquiry into the circumstances surrounding a particular unplanned event or potential event for the purposes of identifying possible causes and contributing factors for the event
   */
  Investigation: basic_resource_type_Investigation,
  /**
   * A request for payment for goods and/or services.  Includes the idea of a healthcare insurance claim.
   */
  Invoice: basic_resource_type_Invoice,
  /**
   * An adjudication of what would be paid under an insurance plan for a hypothetical claim for goods or services
   */
  Predetermination: basic_resource_type_Predetermination,
  /**
   * A request for a pre-determination of the cost that would be paid under an insurance plan for a hypothetical claim for goods or services
   */
  PreDeterminationRequest: basic_resource_type_PreDeterminationRequest,
  /**
   * A set of (possibly conditional) steps to be taken to achieve some aim.  Includes study protocols, treatment protocols, emergency protocols, etc.
   */
  Protocol: basic_resource_type_Protocol,
  /**
   * A request that care of a particular type be provided to a patient.  Could involve the transfer of care, a consult, etc.
   */
  Referral: basic_resource_type_Referral,
  /**
   * An investigation to determine information about a particular therapy or product
   */
  Study: basic_resource_type_Study,
  /**
   * The transition of a patient or set of material from one location to another
   */
  Transfer: basic_resource_type_Transfer,
};
const benefit_network_InNetwork: Coding = {
  code: 'in',
  display: 'In Network',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-network',
};
const benefit_network_OutOfNetwork: Coding = {
  code: 'out',
  display: 'Out of Network',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-network',
};
/**
 * This value set includes a smattering of Network type codes.
 */
export const BenefitNetwork = {
  /**
   * Services rendered by a Network provider
   */
  InNetwork: benefit_network_InNetwork,
  /**
   * Services rendered by a provider who is not in the Network
   */
  OutOfNetwork: benefit_network_OutOfNetwork,
};
const benefit_term_Annual: Coding = {
  code: 'annual',
  display: 'Annual',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-term',
};
const benefit_term_Day: Coding = {
  code: 'day',
  display: 'Day',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-term',
};
const benefit_term_Lifetime: Coding = {
  code: 'lifetime',
  display: 'Lifetime',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-term',
};
/**
 * This value set includes a smattering of Benefit Term codes.
 */
export const BenefitTerm = {
  /**
   * Annual, renewing on the anniversary
   */
  Annual: benefit_term_Annual,
  /**
   * Per day
   */
  Day: benefit_term_Day,
  /**
   * For the total term, lifetime, of the policy or coverage
   */
  Lifetime: benefit_term_Lifetime,
};
const benefit_type_Benefit: Coding = {
  code: 'benefit',
  display: 'Benefit',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-type',
};
const benefit_type_CopaymentPerService: Coding = {
  code: 'copay',
  display: 'Copayment per service',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-type',
};
const benefit_type_CopaymentMaximumPerService: Coding = {
  code: 'copay-maximum',
  display: 'Copayment maximum per service',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-type',
};
const benefit_type_CopaymentPercentPerService: Coding = {
  code: 'copay-percent',
  display: 'Copayment Percent per service',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-type',
};
const benefit_type_Deductible: Coding = {
  code: 'deductible',
  display: 'Deductible',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-type',
};
const benefit_type_MedicalPrimaryHealthCoverage: Coding = {
  code: 'medical-primarycare',
  display: 'Medical Primary Health Coverage',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-type',
};
const benefit_type_PharmacyDispenseCoverage: Coding = {
  code: 'pharmacy-dispense',
  display: 'Pharmacy Dispense Coverage',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-type',
};
const benefit_type_Room: Coding = {
  code: 'room',
  display: 'Room',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-type',
};
const benefit_type_VisionContactsCoverage: Coding = {
  code: 'vision-contacts',
  display: 'Vision Contacts Coverage',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-type',
};
const benefit_type_VisionExam: Coding = {
  code: 'vision-exam',
  display: 'Vision Exam',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-type',
};
const benefit_type_VisionGlasses: Coding = {
  code: 'vision-glasses',
  display: 'Vision Glasses',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-type',
};
const benefit_type_Visit: Coding = {
  code: 'visit',
  display: 'Visit',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-type',
};
/**
 * This value set includes a smattering of Benefit type codes.
 */
export const BenefitType = {
  /**
   * Maximum benefit allowable.
   */
  Benefit: benefit_type_Benefit,
  /**
   * Copayment per service
   */
  CopaymentPerService: benefit_type_CopaymentPerService,
  /**
   * Copayment maximum per service
   */
  CopaymentMaximumPerService: benefit_type_CopaymentMaximumPerService,
  /**
   * Copayment percentage per service
   */
  CopaymentPercentPerService: benefit_type_CopaymentPercentPerService,
  /**
   * Cost to be incurred before benefits are applied
   */
  Deductible: benefit_type_Deductible,
  /**
   * Medical Primary Health Coverage
   */
  MedicalPrimaryHealthCoverage: benefit_type_MedicalPrimaryHealthCoverage,
  /**
   * Pharmacy Dispense Coverage
   */
  PharmacyDispenseCoverage: benefit_type_PharmacyDispenseCoverage,
  /**
   * Type of room
   */
  Room: benefit_type_Room,
  /**
   * Contact Lenses
   */
  VisionContactsCoverage: benefit_type_VisionContactsCoverage,
  /**
   * Vision Exam
   */
  VisionExam: benefit_type_VisionExam,
  /**
   * Frames and lenses
   */
  VisionGlasses: benefit_type_VisionGlasses,
  /**
   * Service visit
   */
  Visit: benefit_type_Visit,
};
const benefit_unit_Family: Coding = {
  code: 'family',
  display: 'Family',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-unit',
};
const benefit_unit_Individual: Coding = {
  code: 'individual',
  display: 'Individual',
  system: 'http://terminology.hl7.org/CodeSystem/benefit-unit',
};
/**
 * This value set includes a smattering of Unit type codes.
 */
export const BenefitUnit = {
  /**
   * A family, typically includes self, spouse(s) and children to a defined age
   */
  Family: benefit_unit_Family,
  /**
   * A single individual
   */
  Individual: benefit_unit_Individual,
};
const binding_strength_Example: Coding = {
  code: 'example',
  display: 'Example',
  system: 'http://hl7.org/fhir/binding-strength',
};
const binding_strength_Extensible: Coding = {
  code: 'extensible',
  display: 'Extensible',
  system: 'http://hl7.org/fhir/binding-strength',
};
const binding_strength_Preferred: Coding = {
  code: 'preferred',
  display: 'Preferred',
  system: 'http://hl7.org/fhir/binding-strength',
};
const binding_strength_Required: Coding = {
  code: 'required',
  display: 'Required',
  system: 'http://hl7.org/fhir/binding-strength',
};
/**
 * Indication of the degree of conformance expectations associated with a binding.
 */
export const BindingStrength = {
  /**
   * Instances are not expected or even encouraged to draw from the specified value set.  The value set merely provides examples of the types of concepts intended to be included.
   */
  Example: binding_strength_Example,
  /**
   * To be conformant, the concept in this element SHALL be from the specified value set if any of the codes within the value set can apply to the concept being communicated.  If the value set does not cover the concept (based on human review), alternate codings (or, data type allowing, text) may be included instead.
   */
  Extensible: binding_strength_Extensible,
  /**
   * Instances are encouraged to draw from the specified codes for interoperability purposes but are not required to do so to be considered conformant.
   */
  Preferred: binding_strength_Preferred,
  /**
   * To be conformant, the concept in this element SHALL be from the specified value set.
   */
  Required: binding_strength_Required,
};
const BodysiteLaterality_UnilateralLeft: Coding = {
  code: '419161000',
  display: 'Unilateral left',
  system: 'http://snomed.info/sct',
};
const BodysiteLaterality_UnilateralRight: Coding = {
  code: '419465000',
  display: 'Unilateral right',
  system: 'http://snomed.info/sct',
};
const BodysiteLaterality_Bilateral: Coding = {
  code: '51440002',
  display: 'Bilateral',
  system: 'http://snomed.info/sct',
};
/**
 * Laterality: SNOMED-CT concepts for 'left', 'right', and 'bilateral'
 */
export const BodysiteLaterality = {
  UnilateralLeft: BodysiteLaterality_UnilateralLeft,
  UnilateralRight: BodysiteLaterality_UnilateralRight,
  Bilateral: BodysiteLaterality_Bilateral,
};
const BodystructureRelativeLocation_Posterior: Coding = {
  code: '255551008',
  display: 'Posterior',
  system: 'http://snomed.info/sct',
};
const BodystructureRelativeLocation_Medial: Coding = {
  code: '255561001',
  display: 'Medial',
  system: 'http://snomed.info/sct',
};
const BodystructureRelativeLocation_Inferior: Coding = {
  code: '261089000',
  display: 'Inferior',
  system: 'http://snomed.info/sct',
};
const BodystructureRelativeLocation_Lower: Coding = {
  code: '261122009',
  display: 'Lower',
  system: 'http://snomed.info/sct',
};
const BodystructureRelativeLocation_Upper: Coding = {
  code: '261183002',
  display: 'Upper',
  system: 'http://snomed.info/sct',
};
const BodystructureRelativeLocation_Superior: Coding = {
  code: '264217000',
  display: 'Superior',
  system: 'http://snomed.info/sct',
};
const BodystructureRelativeLocation_Below: Coding = {
  code: '351726001',
  display: 'Below',
  system: 'http://snomed.info/sct',
};
const BodystructureRelativeLocation_Above: Coding = {
  code: '352730000',
  display: 'Above',
  system: 'http://snomed.info/sct',
};
const BodystructureRelativeLocation_Lateral: Coding = {
  code: '49370004',
  display: 'Lateral',
  system: 'http://snomed.info/sct',
};
/**
 * SNOMED-CT concepts modifying the anatomic location
 */
export const BodystructureRelativeLocation = {
  Posterior: BodystructureRelativeLocation_Posterior,
  Medial: BodystructureRelativeLocation_Medial,
  Inferior: BodystructureRelativeLocation_Inferior,
  Lower: BodystructureRelativeLocation_Lower,
  Upper: BodystructureRelativeLocation_Upper,
  Superior: BodystructureRelativeLocation_Superior,
  Below: BodystructureRelativeLocation_Below,
  Above: BodystructureRelativeLocation_Above,
  UnilateralLeft: BodysiteLaterality_UnilateralLeft,
  UnilateralRight: BodysiteLaterality_UnilateralRight,
  Lateral: BodystructureRelativeLocation_Lateral,
  Bilateral: BodysiteLaterality_Bilateral,
};
const bundle_type_Batch: Coding = {
  code: 'batch',
  display: 'Batch',
  system: 'http://hl7.org/fhir/bundle-type',
};
const bundle_type_BatchResponse: Coding = {
  code: 'batch-response',
  display: 'Batch Response',
  system: 'http://hl7.org/fhir/bundle-type',
};
const bundle_type_Collection: Coding = {
  code: 'collection',
  display: 'Collection',
  system: 'http://hl7.org/fhir/bundle-type',
};
const bundle_type_Document: Coding = {
  code: 'document',
  display: 'Document',
  system: 'http://hl7.org/fhir/bundle-type',
};
const bundle_type_HistoryList: Coding = {
  code: 'history',
  display: 'History List',
  system: 'http://hl7.org/fhir/bundle-type',
};
const bundle_type_Message: Coding = {
  code: 'message',
  display: 'Message',
  system: 'http://hl7.org/fhir/bundle-type',
};
const bundle_type_SearchResults: Coding = {
  code: 'searchset',
  display: 'Search Results',
  system: 'http://hl7.org/fhir/bundle-type',
};
const bundle_type_Transaction: Coding = {
  code: 'transaction',
  display: 'Transaction',
  system: 'http://hl7.org/fhir/bundle-type',
};
const bundle_type_TransactionResponse: Coding = {
  code: 'transaction-response',
  display: 'Transaction Response',
  system: 'http://hl7.org/fhir/bundle-type',
};
/**
 * Indicates the purpose of a bundle - how it is intended to be used.
 */
export const BundleType = {
  /**
   * The bundle is a set of actions - intended to be processed by a server as a group of independent actions.
   */
  Batch: bundle_type_Batch,
  /**
   * The bundle is a batch response. Note that as a batch, some responses may indicate failure and others success.
   */
  BatchResponse: bundle_type_BatchResponse,
  /**
   * The bundle is a set of resources collected into a single package for ease of distribution that imposes no processing obligations or behavioral rules beyond persistence.
   */
  Collection: bundle_type_Collection,
  /**
   * The bundle is a document. The first resource is a Composition.
   */
  Document: bundle_type_Document,
  /**
   * The bundle is a list of resources from a history interaction on a server.
   */
  HistoryList: bundle_type_HistoryList,
  /**
   * The bundle is a message. The first resource is a MessageHeader.
   */
  Message: bundle_type_Message,
  /**
   * The bundle is a list of resources returned as a result of a search/query interaction, operation, or message.
   */
  SearchResults: bundle_type_SearchResults,
  /**
   * The bundle is a transaction - intended to be processed by a server as an atomic commit.
   */
  Transaction: bundle_type_Transaction,
  /**
   * The bundle is a transaction response. Because the response is a transaction response, the transaction has succeeded, and all responses are error free.
   */
  TransactionResponse: bundle_type_TransactionResponse,
};
const C80DocTypecodes_VAL18HydroxydeoxycorticosteroneMassVolumeInSerumOrPlasma: Coding = {
  code: '11206-0',
  display: '18-Hydroxydeoxycorticosterone [Mass/volume] in Serum or Plasma',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnesthesiaRecords: Coding = {
  code: '11485-0',
  display: 'Anesthesia records',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChemotherapyRecords: Coding = {
  code: '11486-8',
  display: 'Chemotherapy records',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ConsultNote: Coding = {
  code: '11488-4',
  display: 'Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianDischargeSummary: Coding = {
  code: '11490-0',
  display: 'Physician Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProviderUnspecifedHistoryAndPhysicalNote: Coding = {
  code: '11492-6',
  display: 'Provider-unspecifed, History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianInitialAssessmentNoteAtFirstEncounter: Coding = {
  code: '11494-2',
  display: 'Physician Initial assessment note at First encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicalTherapyInitialAssessmentNoteAtFirstEncounter: Coding = {
  code: '11495-9',
  display: 'Physical therapy Initial assessment note at First encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PodiatryInitialAssessmentNoteAtFirstEncounter: Coding = {
  code: '11496-7',
  display: 'Podiatry Initial assessment note at First encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychologyInitialAssessmentNoteAtFirstEncounter: Coding = {
  code: '11497-5',
  display: 'Psychology Initial assessment note at First encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SocialWorkInitialAssessmentNoteAtFirstEncounter: Coding = {
  code: '11498-3',
  display: 'Social work Initial assessment note at First encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalTherapyInitialAssessmentNoteAtFirstEncounter: Coding = {
  code: '11500-6',
  display: 'Occupational therapy Initial assessment note at First encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LaboratoryReport: Coding = {
  code: '11502-2',
  display: 'Laboratory report',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MedicalRecords: Coding = {
  code: '11503-0',
  display: 'Medical records',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProviderUnspecifiedOperationNote: Coding = {
  code: '11504-8',
  display: 'Provider-unspecified Operation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianProcedureNote: Coding = {
  code: '11505-5',
  display: 'Physician procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProviderUnspecifiedProgressNote: Coding = {
  code: '11506-3',
  display: 'Provider-unspecified Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalTherapyProgressNote: Coding = {
  code: '11507-1',
  display: 'Occupational therapy Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicalTherapyProgressNote: Coding = {
  code: '11508-9',
  display: 'Physical therapy Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PodiatryProgressNote: Coding = {
  code: '11509-7',
  display: 'Podiatry Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychologyProgressNote: Coding = {
  code: '11510-5',
  display: 'Psychology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechLanguagePathologyProgressNote: Coding = {
  code: '11512-1',
  display: 'Speech-language pathology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChiropracticRecordsTotalEncounter: Coding = {
  code: '11514-7',
  display: 'Chiropractic Records total Encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicalTherapyRecordsTotalEncounter: Coding = {
  code: '11515-4',
  display: 'Physical therapy Records total Encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianRecordsTotalEncounter: Coding = {
  code: '11516-2',
  display: 'Physician Records total Encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PodiatryRecordsTotalEncounter: Coding = {
  code: '11517-0',
  display: 'Podiatry Records total Encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychologyRecordsTotalEncounter: Coding = {
  code: '11518-8',
  display: 'Psychology Records total Encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SocialServiceRecordsTotalEncounter: Coding = {
  code: '11519-6',
  display: 'Social service Records total Encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechTherapyRecordsTotalEncounter: Coding = {
  code: '11520-4',
  display: 'Speech therapy Records total Encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalTherapyRecordsTotalEncounter: Coding = {
  code: '11521-2',
  display: 'Occupational therapy Records total Encounter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EEGStudy: Coding = {
  code: '11523-8',
  display: 'EEG study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EKGStudy: Coding = {
  code: '11524-6',
  display: 'EKG study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USPelvisAndFetusForPregnancy: Coding = {
  code: '11525-3',
  display: 'US Pelvis and Fetus for pregnancy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PathologyStudy: Coding = {
  code: '11526-1',
  display: 'Pathology study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychiatryStudy: Coding = {
  code: '11527-9',
  display: 'Psychiatry study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgicalPathologyStudy: Coding = {
  code: '11529-5',
  display: 'Surgical pathology study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemperatureCharts: Coding = {
  code: '11534-5',
  display: 'Temperature charts',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseNotes: Coding = {
  code: '11536-0',
  display: 'Nurse Notes',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIBrainStudy: Coding = {
  code: '11541-0',
  display: 'MRI Brain study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseryRecords: Coding = {
  code: '11543-6',
  display: 'Nursery records',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL18HydroxydeoxycortisolCreatinineMassRatioInUrine: Coding = {
  code: '13480-9',
  display: '18-Hydroxydeoxycortisol/Creatinine [Mass Ratio] in Urine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProviderUnspecifiedEDProgressNote: Coding = {
  code: '15507-7',
  display: 'Provider-unspecified ED Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LaborAndDeliveryRecords: Coding = {
  code: '15508-5',
  display: 'Labor and delivery records',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassTimeIn24HourUrine: Coding = {
  code: '16110-9',
  display: '11-Deoxycorticosterone [Mass/time] in 24 hour Urine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasmaPostXXXChallenge: Coding = {
  code: '16294-1',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma: Coding = {
  code: '1656-8',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanStudyReport: Coding = {
  code: '17787-3',
  display: 'Thyroid Scan Study report',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychiatricServiceAttachment: Coding = {
  code: '18594-2',
  display: 'Psychiatric service attachment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianAttendingProgressNote: Coding = {
  code: '18733-6',
  display: 'Physician attending Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalTherapyInitialAssessmentNote: Coding = {
  code: '18734-4',
  display: 'Occupational therapy Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicalTherapyInitialAssessmentNote: Coding = {
  code: '18735-1',
  display: 'Physical therapy Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianInitialAssessmentNote: Coding = {
  code: '18736-9',
  display: 'Physician Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PodiatryInitialAssessmentNote: Coding = {
  code: '18737-7',
  display: 'Podiatry Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychologyInitialAssessmentNote: Coding = {
  code: '18738-5',
  display: 'Psychology Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SocialWorkInitialAssessmentNote: Coding = {
  code: '18739-3',
  display: 'Social work Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechLanguagePathologyInitialAssessmentNote: Coding = {
  code: '18740-1',
  display: 'Speech-language pathology Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ArthroscopyStudy: Coding = {
  code: '18742-7',
  display: 'Arthroscopy study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AutopsyReport: Coding = {
  code: '18743-5',
  display: 'Autopsy report',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BronchoscopyStudy: Coding = {
  code: '18744-3',
  display: 'Bronchoscopy study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiacCatheterizationStudy: Coding = {
  code: '18745-0',
  display: 'Cardiac catheterization study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColonoscopyStudy: Coding = {
  code: '18746-8',
  display: 'Colonoscopy study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DiagnosticImagingStudy: Coding = {
  code: '18748-4',
  display: 'Diagnostic imaging study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElectromyogramStudy: Coding = {
  code: '18749-2',
  display: 'Electromyogram study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElectrophysiologyStudy: Coding = {
  code: '18750-0',
  display: 'Electrophysiology study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EndoscopyStudy: Coding = {
  code: '18751-8',
  display: 'Endoscopy study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExerciseStressTestStudy: Coding = {
  code: '18752-6',
  display: 'Exercise stress test study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FlexibleSigmoidoscopyStudy: Coding = {
  code: '18753-4',
  display: 'Flexible sigmoidoscopy study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HolterMonitorStudy: Coding = {
  code: '18754-2',
  display: 'Holter monitor study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRISpineStudy: Coding = {
  code: '18756-7',
  display: 'MRI Spine study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpirometryStudy: Coding = {
  code: '18759-1',
  display: 'Spirometry study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProviderUnspecifiedTransferSummary: Coding = {
  code: '18761-7',
  display: 'Provider-unspecified Transfer summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianConsultingInitialAssessmentNote: Coding = {
  code: '18763-3',
  display: 'Physician consulting Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AlcoholAndOrSubstanceAbuseServiceAttachment: Coding = {
  code: '18823-5',
  display: 'Alcohol and/or substance abuse service attachment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiacServiceAttachment: Coding = {
  code: '18824-3',
  display: 'Cardiac service attachment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MedicalSocialServicesAttachment: Coding = {
  code: '18825-0',
  display: 'Medical social services attachment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalTherapyServiceAttachment: Coding = {
  code: '18826-8',
  display: 'Occupational therapy service attachment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiacStressStudyProcedure: Coding = {
  code: '18836-7',
  display: 'Cardiac stress study Procedure',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalConsultationsDocument: Coding = {
  code: '18841-7',
  display: 'Hospital consultations Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DischargeSummary: Coding = {
  code: '18842-5',
  display: 'Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicalTherapyServiceAttachment: Coding = {
  code: '19002-5',
  display: 'Physical therapy service attachment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RespiratoryTherapyServiceAttachment: Coding = {
  code: '19003-3',
  display: 'Respiratory therapy service attachment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkilledNursingServiceAttachment: Coding = {
  code: '19004-1',
  display: 'Skilled nursing service attachment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SourceOfDocumentUsedToAbstractCancer: Coding = {
  code: '21862-8',
  display: 'Source of document used to abstract Cancer',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenRetroperitoneumUS: Coding = {
  code: '24531-6',
  display: 'Abdomen retroperitoneum US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenRUQUS: Coding = {
  code: '24532-4',
  display: 'Abdomen RUQ US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalVesselsMRIAngiogramWContrastIV: Coding = {
  code: '24533-2',
  display: 'Abdominal vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalVesselsUSDoppler: Coding = {
  code: '24534-0',
  display: 'Abdominal vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcetabulumXRay: Coding = {
  code: '24535-7',
  display: 'Acetabulum X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointXRay: Coding = {
  code: '24536-5',
  display: 'Acromioclavicular Joint X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForRemovalOfAmnioticFluidFromUterus: Coding = {
  code: '24537-3',
  display: 'US Guidance for removal of amniotic fluid from Uterus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleMRI: Coding = {
  code: '24538-1',
  display: 'Ankle MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleMRIWAndWOContrastIV: Coding = {
  code: '24539-9',
  display: 'Ankle MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRay2Views: Coding = {
  code: '24540-7',
  display: 'Ankle X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRay: Coding = {
  code: '24541-5',
  display: 'Ankle X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnusUS: Coding = {
  code: '24542-3',
  display: 'Anus US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '24543-1',
  display: 'Aorta Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaThoracicCT: Coding = {
  code: '24544-9',
  display: 'Aorta thoracic CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaThoracicCTWContrastIV: Coding = {
  code: '24545-6',
  display: 'Aorta thoracic CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaArchAndNeckFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24546-4',
  display: 'Aorta arch and Neck Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaUS: Coding = {
  code: '24547-2',
  display: 'Aorta US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AppendixUS: Coding = {
  code: '24548-0',
  display: 'Appendix US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsMRIAngiogramWContrastIV: Coding = {
  code: '24549-8',
  display: 'Upper extremity vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinsFluoroscopicAngiogramWContrastIV: Coding = {
  code: '24550-6',
  display: 'Upper extremity veins Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AVFistulaFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24551-4',
  display: 'AV fistula Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_StentFluoroscopyWContrastIntraStent: Coding = {
  code: '24552-2',
  display: 'Stent Fluoroscopy W contrast intra stent',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VesselIntracranialFluoroscopicAngiogramEmbolectomyWContrastIV: Coding = {
  code: '24553-0',
  display: 'Vessel intracranial Fluoroscopic angiogram Embolectomy W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ArteryFluoroscopicAngiogramEmbolizationWContrastIA: Coding = {
  code: '24554-8',
  display: 'Artery Fluoroscopic angiogram Embolization W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInArtery: Coding = {
  code: '24555-5',
  display: 'Fluoroscopic angiogram Guidance for placement of stent in Artery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenMRI: Coding = {
  code: '24556-3',
  display: 'Abdomen MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenMRIWAndWOContrastIV: Coding = {
  code: '24557-1',
  display: 'Abdomen MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenUS: Coding = {
  code: '24558-9',
  display: 'Abdomen US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForRemovalOfFluidFromAbdomen: Coding = {
  code: '24559-7',
  display: 'US Guidance for removal of fluid from Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPLeftLateralDecubitusPortable: Coding = {
  code: '24560-5',
  display: 'Abdomen X-ray AP left lateral-decubitus portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPLeftLateralDecubitus: Coding = {
  code: '24561-3',
  display: 'Abdomen X-ray AP left lateral-decubitus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPLeftLateralDecubitusAndRightLateralDecubitus: Coding = {
  code: '24562-1',
  display: 'Abdomen X-ray AP (left lateral-decubitus and right lateral-decubitus)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPRightLateralDecubitus: Coding = {
  code: '24563-9',
  display: 'Abdomen X-ray AP right lateral-decubitus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPUprightPortable: Coding = {
  code: '24564-7',
  display: 'Abdomen X-ray AP upright portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenRetroperitoneumCT: Coding = {
  code: '24566-2',
  display: 'Abdomen retroperitoneum CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenRetroperitoneumCTWContrast: Coding = {
  code: '24567-0',
  display: 'Abdomen retroperitoneum CT W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AVFistulaFluoroscopicAngiogramAtherectomyWContrastIV: Coding = {
  code: '24568-8',
  display: 'AV fistula Fluoroscopic angiogram Atherectomy W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AVShuntFluoroscopicAngiogramWContrastIV: Coding = {
  code: '24569-6',
  display: 'AV shunt Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForStoneRemovalOfBiliaryDuctCommonWContrastIntraBiliaryDuct: Coding = {
  code: '24570-4',
  display: 'Fluoroscopy Guidance for stone removal of Biliary duct common-- W contrast intra biliary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BiliaryDuctsAndGallbladderScanForPatencyOfBiliaryStructuresAndEjectionFractionWSincalideAndWRadionuclideIV: Coding = {
  code: '24571-2',
  display: 'Biliary ducts and Gallbladder Scan for patency of biliary structures and ejection fraction W sincalide and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BiliaryDuctsAndGallbladderScanForPatencyOfBiliaryStructuresWTc99mIV: Coding = {
  code: '24572-0',
  display: 'Biliary ducts and Gallbladder Scan for patency of biliary structures W Tc-99m IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BiliaryDuctsAndGallbladderXRayWContrastIV: Coding = {
  code: '24573-8',
  display: 'Biliary ducts and Gallbladder X-ray W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BiliaryDuctsAndGallbladderFluoroscopyDuringSurgeryWContrastBiliaryDuct: Coding = {
  code: '24574-6',
  display: 'Biliary ducts and Gallbladder Fluoroscopy during surgery W contrast biliary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BiliaryDuctsAndGallbladderFluoroscopyWContrastPercutaneousTranshepatic: Coding = {
  code: '24575-3',
  display: 'Biliary ducts and Gallbladder Fluoroscopy W contrast percutaneous transhepatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrinaryBladderArteriesFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24576-1',
  display: 'Urinary bladder arteries Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneXRayDuringSurgery: Coding = {
  code: '24577-9',
  display: 'Bone X-ray during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BonesSPECT: Coding = {
  code: '24578-7',
  display: 'Bones SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BonesLongXRaySurvey: Coding = {
  code: '24579-5',
  display: 'Bones long X-ray survey',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrachiocephalicArteryFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '24580-3',
  display: 'Brachiocephalic artery Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrachialArteryAndSubclavianArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24581-1',
  display: 'Brachial artery and Subclavian artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletMRI: Coding = {
  code: '24582-9',
  display: 'Thoracic outlet MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletMRIWAndWOContrastIV: Coding = {
  code: '24583-7',
  display: 'Thoracic outlet MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletVesselsMRIAngiogramWContrastIV: Coding = {
  code: '24584-5',
  display: 'Thoracic outlet vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceStereotacticForBiopsyOfHeadWContrastIV: Coding = {
  code: '24585-2',
  display: 'CT Guidance.stereotactic for biopsy of Head-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainMRIWAnesthesia: Coding = {
  code: '24586-0',
  display: 'Brain MRI W anesthesia',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainMRIWAndWOContrastIV: Coding = {
  code: '24587-8',
  display: 'Brain MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainMRIWAndWOContrastIVAndWAnesthesia: Coding = {
  code: '24588-6',
  display: 'Brain MRI W and WO contrast IV and W anesthesia',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainMRIWContrastIV: Coding = {
  code: '24589-4',
  display: 'Brain MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainMRI: Coding = {
  code: '24590-2',
  display: 'Brain MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanBrainDeathProtocolWTc99mHMPAOIV: Coding = {
  code: '24591-0',
  display: 'Brain Scan brain death protocol W Tc-99m HMPAO IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVesselsMRIAngiogramWContrastIV: Coding = {
  code: '24593-6',
  display: 'Head vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForAspirationOfCystOfBreast: Coding = {
  code: '24594-4',
  display: 'Mammogram Guidance for aspiration of cyst of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForNeedleLocalizationOfMassOfBreast: Coding = {
  code: '24595-1',
  display: 'Mammogram Guidance for needle localization of mass of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastSpecimenUS: Coding = {
  code: '24596-9',
  display: 'Breast specimen US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastSpecimenMammogram: Coding = {
  code: '24597-7',
  display: 'Breast specimen Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForAspirationOfBreast: Coding = {
  code: '24598-5',
  display: 'Mammogram Guidance for aspiration of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastUSLimited: Coding = {
  code: '24599-3',
  display: 'Breast US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleLocalizationOfBreast: Coding = {
  code: '24600-9',
  display: 'US Guidance for needle localization of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastUS: Coding = {
  code: '24601-7',
  display: 'Breast US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForBiopsyOfBreast: Coding = {
  code: '24602-5',
  display: 'Mammogram Guidance for biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceStereotacticForBiopsyOfBreast: Coding = {
  code: '24603-3',
  display: 'Mammogram Guidance.stereotactic for biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogramDiagnosticLimited: Coding = {
  code: '24604-1',
  display: 'Breast Mammogram diagnostic limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogramDiagnostic: Coding = {
  code: '24605-8',
  display: 'Breast Mammogram diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogramScreening: Coding = {
  code: '24606-6',
  display: 'Breast Mammogram screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForCoreNeedlePercutaneousBiopsyOfBreast: Coding = {
  code: '24609-0',
  display: 'Mammogram Guidance for core needle percutaneous biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogramLimited: Coding = {
  code: '24610-8',
  display: 'Breast Mammogram limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OutpatientConsultation2ndOpinion: Coding = {
  code: '24611-6',
  display: 'Outpatient Consultation 2nd opinion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusXRay: Coding = {
  code: '24612-4',
  display: 'Calcaneus X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInArteryInCentralCardiovascularArtery: Coding = {
  code: '24613-2',
  display: 'Fluoroscopic angiogram Guidance for placement of catheter in artery in Central cardiovascular artery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryExtracranialFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '24614-0',
  display: 'Carotid artery extracranial Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryIntracranialFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '24615-7',
  display: 'Carotid artery intracranial Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryUS: Coding = {
  code: '24616-5',
  display: 'Carotid artery US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24617-3',
  display: 'Carotid artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRay: Coding = {
  code: '24619-9',
  display: 'Wrist X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CatheterFluoroscopyPatencyCheckWContrastViaCatheter: Coding = {
  code: '24620-7',
  display: 'Catheter Fluoroscopy Patency check W contrast via catheter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfCavity: Coding = {
  code: '24621-5',
  display: 'Fluoroscopy Guidance for percutaneous drainage of Cavity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CeliacArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24622-3',
  display: 'Celiac artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAnestheticBlockInjectionOfCeliacPlexus: Coding = {
  code: '24623-1',
  display: 'CT Guidance for anesthetic block injection of Celiac plexus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForChangeOfCentralCatheterInCentralVeinWContrastIV: Coding = {
  code: '24624-9',
  display: 'Fluoroscopic angiogram Guidance for change of central catheter in Central vein-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInCentralVeinWContrastIV: Coding = {
  code: '24625-6',
  display: 'Fluoroscopic angiogram Guidance for placement of catheter in Central vein-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForRepositionOfCatheterInCentralVeinWContrastIV: Coding = {
  code: '24626-4',
  display: 'Fluoroscopic angiogram Guidance for reposition of catheter in Central vein-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestCT: Coding = {
  code: '24627-2',
  display: 'Chest CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestCTWContrastIV: Coding = {
  code: '24628-0',
  display: 'Chest CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestMRI: Coding = {
  code: '24629-8',
  display: 'Chest MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestUS: Coding = {
  code: '24630-6',
  display: 'Chest US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyCentralVeinCatheterPlacementCheck: Coding = {
  code: '24631-4',
  display: 'Unspecified body region Fluoroscopy Central vein catheter placement check',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPPortable: Coding = {
  code: '24632-2',
  display: 'Chest X-ray AP portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPortableWInspirationAndExpiration: Coding = {
  code: '24634-8',
  display: 'Chest X-ray portable W inspiration and expiration',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAUprightWInspirationAndExpiration: Coding = {
  code: '24635-5',
  display: 'Chest X-ray PA upright W inspiration and expiration',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPLeftLateralDecubitusPortable: Coding = {
  code: '24636-3',
  display: 'Chest X-ray AP left lateral-decubitus portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPLeftLateralDecubitus: Coding = {
  code: '24637-1',
  display: 'Chest X-ray AP left lateral-decubitus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayLeftLateralUprightPortable: Coding = {
  code: '24638-9',
  display: 'Chest X-ray left lateral upright portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayLeftLateralUpright: Coding = {
  code: '24639-7',
  display: 'Chest X-ray left lateral upright',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayLordotic: Coding = {
  code: '24640-5',
  display: 'Chest X-ray lordotic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayLeftObliquePortable: Coding = {
  code: '24641-3',
  display: 'Chest X-ray left oblique portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPAndPAUpright: Coding = {
  code: '24642-1',
  display: 'Chest X-ray AP and PA upright',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateralAndRightOrLeftObliqueUpright: Coding = {
  code: '24643-9',
  display: 'Chest X-ray PA and lateral and right or-left oblique upright',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateralUprightPortable: Coding = {
  code: '24644-7',
  display: 'Chest X-ray PA and lateral upright portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndRightLateralAndRightObliqueAndLeftObliqueUprightPortable: Coding = {
  code: '24645-4',
  display: 'Chest X-ray PA and right lateral and right oblique and left oblique upright portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndRightLateralAndRightObliqueAndLeftObliqueUpright: Coding = {
  code: '24646-2',
  display: 'Chest X-ray PA and right lateral and right oblique and left oblique upright',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateralUpright: Coding = {
  code: '24647-0',
  display: 'Chest X-ray PA and lateral upright',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAUpright: Coding = {
  code: '24648-8',
  display: 'Chest X-ray PA upright',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPRightLateralDecubitusAndLeftLateralDecubitusPortable: Coding = {
  code: '24649-6',
  display: 'Chest X-ray AP (right lateral-decubitus and left lateral-decubitus) portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPRightLateralDecubitusAndLeftLateralDecubitus: Coding = {
  code: '24650-4',
  display: 'Chest X-ray AP (right lateral-decubitus and left lateral-decubitus)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayRightObliqueAndLeftObliqueUpright: Coding = {
  code: '24651-2',
  display: 'Chest X-ray right oblique and left oblique upright',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPRightLateralDecubitusPortable: Coding = {
  code: '24652-0',
  display: 'Chest X-ray AP right lateral-decubitus portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPAndAPRightLateralDecubitus: Coding = {
  code: '24653-8',
  display: 'Chest X-ray AP and AP right lateral-decubitus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPAndAPRightLateralDecubitusPortable: Coding = {
  code: '24654-6',
  display: 'Chest X-ray AP and AP right lateral-decubitus portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestFluoroscopyImageIntensifierDuringSurgery: Coding = {
  code: '24655-3',
  display: 'Chest Fluoroscopy Image intensifier during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestFluoroscopyDuringSurgery: Coding = {
  code: '24656-1',
  display: 'Chest Fluoroscopy during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayTomograph: Coding = {
  code: '24657-9',
  display: 'Chest X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24658-7',
  display: 'Aorta Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestVesselsMRIAngiogramWContrastIV: Coding = {
  code: '24659-5',
  display: 'Chest vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaThoracicMRIAngiogram: Coding = {
  code: '24660-3',
  display: 'Aorta thoracic MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PleuralSpaceFluoroscopyWContrastIntraPleuralSpace: Coding = {
  code: '24661-1',
  display: 'Pleural space Fluoroscopy W contrast intra pleural space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfPleuralSpace: Coding = {
  code: '24662-9',
  display: 'US Guidance for aspiration of Pleural space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCisternScanWRadionuclideIT: Coding = {
  code: '24663-7',
  display: 'Head Cistern Scan W radionuclide IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleXRay: Coding = {
  code: '24664-5',
  display: 'Clavicle X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumAndCoccyxXRay: Coding = {
  code: '24665-2',
  display: 'Sacrum and Coccyx X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColonFluoroscopyWAirAndBariumContrastPR: Coding = {
  code: '24666-0',
  display: 'Colon Fluoroscopy W air and barium contrast PR',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColonFluoroscopyWContrastPR: Coding = {
  code: '24667-8',
  display: 'Colon Fluoroscopy W contrast PR',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColonFluoroscopyTransitPostSolidContrast: Coding = {
  code: '24668-6',
  display: 'Colon Fluoroscopy transit Post solid contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColonFluoroscopyWWaterSolubleContrastPR: Coding = {
  code: '24669-4',
  display: 'Colon Fluoroscopy W water soluble contrast PR',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfCystOfUnspecifiedBodyRegion: Coding = {
  code: '24670-2',
  display: 'US Guidance for biopsy of cyst of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForAspirationOfCystOfUnspecifiedBodyRegion: Coding = {
  code: '24671-0',
  display: 'Fluoroscopy Guidance for aspiration of cyst of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DiaphragmUSMotion: Coding = {
  code: '24672-8',
  display: 'Diaphragm US Motion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DuodenumFluoroscopyWContrastPOAndHypotonicAgentPerNg: Coding = {
  code: '24673-6',
  display: 'Duodenum Fluoroscopy W contrast PO and hypotonic agent per ng',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowMRI: Coding = {
  code: '24674-4',
  display: 'Elbow MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowMRIWAndWOContrastIV: Coding = {
  code: '24675-1',
  display: 'Elbow MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowXRay: Coding = {
  code: '24676-9',
  display: 'Elbow X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisUSTransvaginal: Coding = {
  code: '24677-7',
  display: 'Pelvis US transvaginal',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EsophagusFluoroscopyWContrastPO: Coding = {
  code: '24678-5',
  display: 'Esophagus Fluoroscopy W contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EsophagusFluoroscopyWGastrografinPO: Coding = {
  code: '24679-3',
  display: 'Esophagus Fluoroscopy W gastrografin PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForDilationOfEsophagus: Coding = {
  code: '24680-1',
  display: 'Fluoroscopy Guidance for dilation of Esophagus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EsophagusAndHypopharynxFluoroscopyVideoWContrastPODuringSwallowing: Coding = {
  code: '24681-9',
  display: 'Esophagus and Hypopharynx Fluoroscopy video W contrast PO during swallowing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EsophagusAndHypopharynxFluoroscopyVideoWLiquidAndPasteContrastPODuringSwallowing: Coding = {
  code: '24682-7',
  display: 'Esophagus and Hypopharynx Fluoroscopy video W liquid and paste contrast PO during swallowing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EsophagusAndStomachScanWTc99mSCPO: Coding = {
  code: '24683-5',
  display: 'Esophagus and Stomach Scan W Tc-99m SC PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtracranialVesselsFluoroscopicAngiogramEmbolectomyWContrastIA: Coding = {
  code: '24684-3',
  display: 'Extracranial vessels Fluoroscopic angiogram Embolectomy W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeripheralVeinsFluoroscopicAngiogramWContrastIV: Coding = {
  code: '24685-0',
  display: 'Peripheral veins Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityXRay: Coding = {
  code: '24686-8',
  display: 'Lower extremity X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointMRI: Coding = {
  code: '24687-6',
  display: 'Lower Extremity Joint MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityMRI: Coding = {
  code: '24688-4',
  display: 'Upper extremity MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityXRay: Coding = {
  code: '24689-2',
  display: 'Upper extremity X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityCT: Coding = {
  code: '24690-0',
  display: 'Extremity CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityCTWContrastIV: Coding = {
  code: '24691-8',
  display: 'Extremity CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfExtremity: Coding = {
  code: '24692-6',
  display: 'US Guidance for drainage of Extremity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityUS: Coding = {
  code: '24693-4',
  display: 'Extremity US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FaceMRIWAndWOContrastIV: Coding = {
  code: '24694-2',
  display: 'Face MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesXRay: Coding = {
  code: '24695-9',
  display: 'Facial bones X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesAndSinusesCT: Coding = {
  code: '24696-7',
  display: 'Facial bones and Sinuses CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesAndSinusesCTWContrastIV: Coding = {
  code: '24697-5',
  display: 'Facial bones and Sinuses CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralArteryFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '24698-3',
  display: 'Femoral artery Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralArteryFluoroscopicAngiogramRunoffWContrastIA: Coding = {
  code: '24699-1',
  display: 'Femoral artery Fluoroscopic angiogram runoff W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurAndTibiaXRayForLegLength: Coding = {
  code: '24700-7',
  display: 'Femur and Tibia X-ray for leg length',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighMRI: Coding = {
  code: '24702-3',
  display: 'Thigh MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighMRIWAndWOContrastIV: Coding = {
  code: '24703-1',
  display: 'Thigh MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurXRay: Coding = {
  code: '24704-9',
  display: 'Femur X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerMRI: Coding = {
  code: '24705-6',
  display: 'Finger MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerXRay: Coding = {
  code: '24706-4',
  display: 'Finger X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootMRI: Coding = {
  code: '24707-2',
  display: 'Foot MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayStanding: Coding = {
  code: '24708-0',
  display: 'Foot X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRay: Coding = {
  code: '24709-8',
  display: 'Foot X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmMRI: Coding = {
  code: '24710-6',
  display: 'Forearm MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GallbladderUS: Coding = {
  code: '24711-4',
  display: 'Gallbladder US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GallbladderXRayWContrastPO: Coding = {
  code: '24712-2',
  display: 'Gallbladder X-ray W contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GallbladderXRay48HoursPostContrastPO: Coding = {
  code: '24713-0',
  display: 'Gallbladder X-ray 48 hours post contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineScanWTc99mTaggedRBCIV: Coding = {
  code: '24714-8',
  display: 'Gastrointestine Scan W Tc-99m tagged RBC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineUpperFluoroscopySingleViewWContrastPO: Coding = {
  code: '24715-5',
  display: 'Gastrointestine upper Fluoroscopy Single view W contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPlacementOfDecompressionTubeInGastrointestine: Coding = {
  code: '24716-3',
  display: 'Fluoroscopy Guidance for placement of decompression tube in Gastrointestine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IlealConduitXRayLoopogram: Coding = {
  code: '24717-1',
  display: 'Ileal conduit X-ray Loopogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForTransjugularBiopsyOfLiverWContrastIV: Coding = {
  code: '24718-9',
  display: 'Fluoroscopy Guidance for transjugular biopsy of Liver-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GroinUS: Coding = {
  code: '24719-7',
  display: 'Groin US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandMRI: Coding = {
  code: '24720-5',
  display: 'Hand MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRay2Views: Coding = {
  code: '24721-3',
  display: 'Hand X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRay3Views: Coding = {
  code: '24722-1',
  display: 'Hand X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRayArthritis: Coding = {
  code: '24723-9',
  display: 'Hand X-ray arthritis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristAndHandXRayBoneAge: Coding = {
  code: '24724-7',
  display: 'Wrist and Hand X-ray bone age',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCT: Coding = {
  code: '24725-4',
  display: 'Head CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCTWAndWOContrastIV: Coding = {
  code: '24726-2',
  display: 'Head CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCTWContrastIV: Coding = {
  code: '24727-0',
  display: 'Head CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCTCine: Coding = {
  code: '24728-8',
  display: 'Head CT cine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCTCineWAndWOContrastIV: Coding = {
  code: '24729-6',
  display: 'Head CT cine W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScan: Coding = {
  code: '24730-4',
  display: 'Brain Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadUS: Coding = {
  code: '24731-2',
  display: 'Head US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadUSDuringSurgery: Coding = {
  code: '24732-0',
  display: 'Head US during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVesselsUSDoppler: Coding = {
  code: '24733-8',
  display: 'Head vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCisternCTWContrastIT: Coding = {
  code: '24734-6',
  display: 'Head Cistern CT W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalAndPosteriorFossaMRI: Coding = {
  code: '24735-3',
  display: 'Internal auditory canal and Posterior fossa MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalAndPosteriorFossaMRIWAndWOContrastIV: Coding = {
  code: '24740-3',
  display: 'Internal auditory canal and Posterior fossa MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PetrousBoneXRay: Coding = {
  code: '24745-2',
  display: 'Petrous bone X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadSagittalSinusMRI: Coding = {
  code: '24746-0',
  display: 'Head Sagittal Sinus MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadSagittalSinusMRIAngiogramWContrastIV: Coding = {
  code: '24747-8',
  display: 'Head Sagittal Sinus MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartMRI: Coding = {
  code: '24748-6',
  display: 'Heart MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanAtRestAndWTl201IV: Coding = {
  code: '24750-2',
  display: 'Heart Scan at rest and W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParathyroidScanWTI201SubtractionTc99mIV: Coding = {
  code: '24751-0',
  display: 'Parathyroid Scan W TI-201 subtraction Tc-99m IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartFluoroscopyVideo: Coding = {
  code: '24752-8',
  display: 'Heart Fluoroscopy video',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCTWContrastIV: Coding = {
  code: '24753-6',
  display: 'Unspecified body region CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdministrationOfVasodilatorIntoCatheterOfVein: Coding = {
  code: '24754-4',
  display: 'Administration of vasodilator into catheter of Vein',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForAtherectomyOfVeinWContrastIV: Coding = {
  code: '24755-1',
  display: 'Fluoroscopic angiogram Guidance for atherectomy of Vein-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInVein: Coding = {
  code: '24756-9',
  display: 'Fluoroscopic angiogram Guidance for placement of stent in Vein',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CoronaryArteriesCTFast: Coding = {
  code: '24757-7',
  display: 'Coronary arteries CT fast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipUS: Coding = {
  code: '24760-1',
  display: 'Hip US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRaySingleView: Coding = {
  code: '24761-9',
  display: 'Hip X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRay: Coding = {
  code: '24762-7',
  display: 'Hip X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipFluoroscopyWContrastIntraarticular: Coding = {
  code: '24764-3',
  display: 'Hip Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusXRay2Views: Coding = {
  code: '24765-0',
  display: 'Humerus X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacArteryFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '24766-8',
  display: 'Iliac artery Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalXRayTomograph: Coding = {
  code: '24767-6',
  display: 'Internal auditory canal X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForInjectionOfJointSpace: Coding = {
  code: '24769-2',
  display: 'CT Guidance for injection of Joint space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JointScanWIn111Intrajoint: Coding = {
  code: '24770-0',
  display: 'Joint Scan W In-111 intrajoint',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForAspirationOfJointSpace: Coding = {
  code: '24771-8',
  display: 'Fluoroscopy Guidance for aspiration of Joint space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfKidney: Coding = {
  code: '24772-6',
  display: 'US Guidance for biopsy of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralScanWRadionuclideTransplantScan: Coding = {
  code: '24773-4',
  display: 'Kidney - bilateral Scan W radionuclide transplant scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralScan: Coding = {
  code: '24776-7',
  display: 'Kidney - bilateral Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralXRay3ViewsSerialWAndWOContrastIV: Coding = {
  code: '24778-3',
  display: 'Kidney - bilateral X-ray 3 views serial W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPlacementOfPercutaneousNephrostomyInKidneyBilateralWContrastViaTube: Coding = {
  code: '24779-1',
  display: 'Fluoroscopy Guidance for placement of percutaneous nephrostomy in Kidney - bilateral-- W contrast via tube',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralFluoroscopyWContrastViaNephrostomyTube: Coding = {
  code: '24780-9',
  display: 'Kidney - bilateral Fluoroscopy W contrast via nephrostomy tube',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForChangeOfPercutaneousNephrostomyTubeInKidneyBilateralWContrast: Coding = {
  code: '24781-7',
  display: 'Fluoroscopy Guidance for change of percutaneous nephrostomy tube in Kidney - bilateral-- W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPlacementOfPercutaneousNephroureteralStentInKidneyBilateral: Coding = {
  code: '24782-5',
  display: 'Fluoroscopy Guidance for placement of percutaneous nephroureteral stent in Kidney - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralFluoroscopyUrodynamics: Coding = {
  code: '24783-3',
  display: 'Kidney - bilateral Fluoroscopy Urodynamics',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralXRayTomographWAndWOContrastIV: Coding = {
  code: '24784-1',
  display: 'Kidney - bilateral X-ray tomograph W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralXRayTomographWOContrastAnd10MPostContrastIV: Coding = {
  code: '24787-4',
  display: 'Kidney - bilateral X-ray tomograph WO contrast and 10M post contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralXRayWContrastIV: Coding = {
  code: '24788-2',
  display: 'Kidney - bilateral X-ray W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralXRayTomograph: Coding = {
  code: '24789-0',
  display: 'Kidney - bilateral X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralXRayTomographWContrastIV: Coding = {
  code: '24790-8',
  display: 'Kidney - bilateral X-ray tomograph W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPAndAPLeftLateralDecubitusPortable: Coding = {
  code: '24792-4',
  display: 'Abdomen X-ray AP and AP left lateral-decubitus portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPAndLateralPortable: Coding = {
  code: '24793-2',
  display: 'Abdomen X-ray AP and lateral portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPAndLateral: Coding = {
  code: '24794-0',
  display: 'Abdomen X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPSupineAndUprightPortable: Coding = {
  code: '24795-7',
  display: 'Abdomen X-ray AP (supine and upright) portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPAndAPLeftLateralDecubitus: Coding = {
  code: '24796-5',
  display: 'Abdomen X-ray AP and AP left lateral-decubitus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPAndObliqueProne: Coding = {
  code: '24797-3',
  display: 'Abdomen X-ray AP and oblique prone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPSupineAndUpright: Coding = {
  code: '24798-1',
  display: 'Abdomen X-ray AP (supine and upright)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPSingleView: Coding = {
  code: '24799-9',
  display: 'Abdomen X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeFluoroscopyWContrastIntraarticular: Coding = {
  code: '24800-5',
  display: 'Knee Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayMerchants: Coding = {
  code: '24801-3',
  display: 'Knee X-ray Merchants',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeMRI: Coding = {
  code: '24802-1',
  display: 'Knee MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeMRIWAndWOContrastIV: Coding = {
  code: '24803-9',
  display: 'Knee MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeScan: Coding = {
  code: '24804-7',
  display: 'Knee Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayAPAndLateralStanding: Coding = {
  code: '24805-4',
  display: 'Knee X-ray AP and lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRay2Views: Coding = {
  code: '24806-2',
  display: 'Knee X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayAPSingleViewStanding: Coding = {
  code: '24807-0',
  display: 'Knee X-ray AP single view standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayAPAndPAStanding: Coding = {
  code: '24808-8',
  display: 'Knee X-ray AP and PA standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayStanding: Coding = {
  code: '24809-6',
  display: 'Knee X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationOfLiver: Coding = {
  code: '24811-2',
  display: 'CT Guidance for aspiration of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfLiver: Coding = {
  code: '24812-0',
  display: 'CT Guidance for biopsy of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForCoreNeedleBiopsyOfLiver: Coding = {
  code: '24813-8',
  display: 'CT Guidance for core needle biopsy of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverCT: Coding = {
  code: '24814-6',
  display: 'Liver CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverCTWContrastIV: Coding = {
  code: '24815-3',
  display: 'Liver CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfLiver: Coding = {
  code: '24816-1',
  display: 'US Guidance for biopsy of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverSPECTWTc99mIV: Coding = {
  code: '24817-9',
  display: 'Liver SPECT W Tc-99m IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverAndDiaphragmUS: Coding = {
  code: '24818-7',
  display: 'Liver and Diaphragm US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverAndSpleenScanWTc99mCalciumColloidIV: Coding = {
  code: '24819-5',
  display: 'Liver and Spleen Scan W Tc-99m calcium colloid IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegVesselsMRIAngiogramWContrastIV: Coding = {
  code: '24820-3',
  display: 'Lower leg vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegMRI: Coding = {
  code: '24821-1',
  display: 'Lower leg MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationOfLung: Coding = {
  code: '24822-9',
  display: 'CT Guidance for aspiration of Lung',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfLung: Coding = {
  code: '24823-7',
  display: 'CT Guidance for biopsy of Lung',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanPortable: Coding = {
  code: '24824-5',
  display: 'Lung Scan portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungXRayWContrastIntrabronchial: Coding = {
  code: '24825-2',
  display: 'Lung X-ray W contrast intrabronchial',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LymphaticsScanWRadionuclideIntraLymphatic: Coding = {
  code: '24826-0',
  display: 'Lymphatics Scan W radionuclide intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LymphaticsFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '24827-8',
  display: 'Lymphatics Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleXRayPanorex: Coding = {
  code: '24828-6',
  display: 'Mandible X-ray panorex',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleXRay: Coding = {
  code: '24829-4',
  display: 'Mandible X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidXRay: Coding = {
  code: '24830-2',
  display: 'Mastoid X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MeckelsDiverticulumScanWTc99mM04IV: Coding = {
  code: '24831-0',
  display: 'Meckels diverticulum Scan W Tc-99m M04 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MesentericArteryFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '24832-8',
  display: 'Mesenteric artery Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MesentericArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24833-6',
  display: 'Mesenteric artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasalBonesXRay: Coding = {
  code: '24834-4',
  display: 'Nasal bones X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasopharynxAndNeckCT: Coding = {
  code: '24835-1',
  display: 'Nasopharynx and Neck CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasopharynxAndNeckCTWContrastIV: Coding = {
  code: '24836-9',
  display: 'Nasopharynx and Neck CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationOfNeck: Coding = {
  code: '24837-7',
  display: 'CT Guidance for aspiration of Neck',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfNeck: Coding = {
  code: '24838-5',
  display: 'CT Guidance for biopsy of Neck',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckMRI: Coding = {
  code: '24839-3',
  display: 'Neck MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckMRIWAndWOContrastIV: Coding = {
  code: '24840-1',
  display: 'Neck MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckMRIWContrastIV: Coding = {
  code: '24841-9',
  display: 'Neck MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckUS: Coding = {
  code: '24842-7',
  display: 'Neck US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckXRayLateral: Coding = {
  code: '24843-5',
  display: 'Neck X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckVesselsMRIAngiogramWContrastIV: Coding = {
  code: '24844-3',
  display: 'Neck vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckFluoroscopyWContrastIntraLarynx: Coding = {
  code: '24845-0',
  display: 'Neck Fluoroscopy W contrast intra larynx',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OpticForamenXRay: Coding = {
  code: '24846-8',
  display: 'Optic foramen X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitBilateralCT: Coding = {
  code: '24848-4',
  display: 'Orbit - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitBilateralCTWAndWOContrastIV: Coding = {
  code: '24849-2',
  display: 'Orbit - bilateral CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitBilateralCTWContrastIV: Coding = {
  code: '24850-0',
  display: 'Orbit - bilateral CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitBilateralMRIWAndWOContrastIV: Coding = {
  code: '24851-8',
  display: 'Orbit - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitBilateralMRIWContrastIV: Coding = {
  code: '24852-6',
  display: 'Orbit - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EyePlusOrbitBilateralUS: Coding = {
  code: '24853-4',
  display: 'Eye+Orbit - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitBilateralXRay: Coding = {
  code: '24854-2',
  display: 'Orbit - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OropharynxFluoroscopyVideo: Coding = {
  code: '24855-9',
  display: 'Oropharynx Fluoroscopy video',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationOfPancreas: Coding = {
  code: '24856-7',
  display: 'CT Guidance for aspiration of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PancreasCT: Coding = {
  code: '24857-5',
  display: 'Pancreas CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PancreasCTWContrastIV: Coding = {
  code: '24858-3',
  display: 'Pancreas CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PancreasUS: Coding = {
  code: '24859-1',
  display: 'Pancreas US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PancreaticArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24860-9',
  display: 'Pancreatic artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaXRay2Views: Coding = {
  code: '24861-7',
  display: 'Patella X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacArteryInternalFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24862-5',
  display: 'Iliac artery Internal Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationOfPelvis: Coding = {
  code: '24863-3',
  display: 'CT Guidance for aspiration of Pelvis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfPelvis: Coding = {
  code: '24864-1',
  display: 'CT Guidance for biopsy of Pelvis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisCT: Coding = {
  code: '24865-8',
  display: 'Pelvis CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisCTWContrastIV: Coding = {
  code: '24866-6',
  display: 'Pelvis CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisMRI: Coding = {
  code: '24867-4',
  display: 'Pelvis MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfPelvis: Coding = {
  code: '24868-2',
  display: 'US Guidance for drainage of Pelvis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisUS: Coding = {
  code: '24869-0',
  display: 'Pelvis US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisVesselsUSDoppler: Coding = {
  code: '24870-8',
  display: 'Pelvis vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayPelvimetry: Coding = {
  code: '24871-6',
  display: 'Pelvis X-ray pelvimetry',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipMRI: Coding = {
  code: '24872-4',
  display: 'Pelvis and Hip MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisVesselsMRIAngiogramWContrastIV: Coding = {
  code: '24873-2',
  display: 'Pelvis vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeripheralArteriesFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24874-0',
  display: 'Peripheral arteries Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeripheralVesselUSDopplerPeripheralPlane: Coding = {
  code: '24875-7',
  display: 'Peripheral vessel US.doppler Peripheral plane',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeritoneovenousShuntScanForPatencyWTc99mDTPAIT: Coding = {
  code: '24876-5',
  display: 'Peritoneovenous shunt Scan for patency W Tc-99m DTPA IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PetrousBoneCT: Coding = {
  code: '24877-3',
  display: 'Petrous bone CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PetrousBoneCTWContrastIV: Coding = {
  code: '24878-1',
  display: 'Petrous bone CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PituitaryAndSellaTurcicaMRIWAndWOContrastIV: Coding = {
  code: '24879-9',
  display: 'Pituitary and Sella turcica MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PituitaryAndSellaTurcicaMRI: Coding = {
  code: '24880-7',
  display: 'Pituitary and Sella turcica MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PoplitealSpaceUS: Coding = {
  code: '24881-5',
  display: 'Popliteal space US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PoplitealArteryFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIA: Coding = {
  code: '24882-3',
  display: 'Popliteal artery Fluoroscopic angiogram Percutaneous transluminal angioplasty of vessel W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfProstate: Coding = {
  code: '24883-1',
  display: 'US Guidance for biopsy of Prostate',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProstateUS: Coding = {
  code: '24884-9',
  display: 'Prostate US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForRepairOfPseudoaneurysmAVFistula: Coding = {
  code: '24885-6',
  display: 'US Guidance for repair of Pseudoaneurysm/AV fistula',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryArteryFluoroscopicAngiogramEmbolectomyWContrastIA: Coding = {
  code: '24887-2',
  display: 'Pulmonary artery Fluoroscopic angiogram Embolectomy W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonarySystemScanVentilationAndPerfusionWXe133InhaledAndWTc99mMAAIV: Coding = {
  code: '24888-0',
  display: 'Pulmonary system Scan ventilation and perfusion W Xe-133 inhaled and W Tc-99m MAA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PylorusUSForPyloricStenosis: Coding = {
  code: '24889-8',
  display: 'Pylorus US for pyloric stenosis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusAndUlnaXRay: Coding = {
  code: '24891-4',
  display: 'Radius and Ulna X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RectumUS: Coding = {
  code: '24892-2',
  display: 'Rectum US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RectumFluoroscopyPostContrastPRDuringDefecation: Coding = {
  code: '24893-0',
  display: 'Rectum Fluoroscopy post contrast PR during defecation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RectumAndUrinaryBladderFluoroscopyWContrastPRAndIntraBladderDuringDefecationAndVoiding: Coding = {
  code: '24894-8',
  display: 'Rectum and Urinary bladder Fluoroscopy W contrast PR and intra bladder during defecation and voiding',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfKidney: Coding = {
  code: '24896-3',
  display: 'US Guidance for drainage of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsXRay: Coding = {
  code: '24899-7',
  display: 'Ribs X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointXRay: Coding = {
  code: '24900-3',
  display: 'Sacroiliac Joint X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForInjectionOfSacroiliacJoint: Coding = {
  code: '24901-1',
  display: 'CT Guidance for injection of Sacroiliac Joint',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SalivaryGlandFluoroscopyWContrastIntraSalivaryDuct: Coding = {
  code: '24902-9',
  display: 'Salivary gland Fluoroscopy W contrast intra salivary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaXRay: Coding = {
  code: '24903-7',
  display: 'Scapula X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PituitaryAndSellaTurcicaCTWAndWOContrastIV: Coding = {
  code: '24904-5',
  display: 'Pituitary and Sella turcica CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderMRI: Coding = {
  code: '24905-2',
  display: 'Shoulder MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderMRIWAndWOContrastIV: Coding = {
  code: '24906-0',
  display: 'Shoulder MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderUS: Coding = {
  code: '24907-8',
  display: 'Shoulder US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRay3Views: Coding = {
  code: '24908-6',
  display: 'Shoulder X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRay: Coding = {
  code: '24909-4',
  display: 'Shoulder X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderFluoroscopyWContrastIntraarticular: Coding = {
  code: '24910-2',
  display: 'Shoulder Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShuntFluoroscopy: Coding = {
  code: '24911-0',
  display: 'Shunt Fluoroscopy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusTractFluoroscopyWContrastIntraSinusTract: Coding = {
  code: '24912-8',
  display: 'Sinus tract Fluoroscopy W contrast intra sinus tract',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesCTLimited: Coding = {
  code: '24913-6',
  display: 'Sinuses CT limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesMRI: Coding = {
  code: '24914-4',
  display: 'Sinuses MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesMRIWContrastIV: Coding = {
  code: '24915-1',
  display: 'Sinuses MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRay: Coding = {
  code: '24916-9',
  display: 'Sinuses X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRaySingleView: Coding = {
  code: '24917-7',
  display: 'Skull X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRay3Views: Coding = {
  code: '24918-5',
  display: 'Skull X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayAPAndLateral: Coding = {
  code: '24919-3',
  display: 'Skull X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayLateral: Coding = {
  code: '24920-1',
  display: 'Skull X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayWaters: Coding = {
  code: '24921-9',
  display: 'Skull X-ray Waters',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRay5Views: Coding = {
  code: '24922-7',
  display: 'Skull X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SmallBowelFluoroscopyViewsEnteroclysisWContrastPOViaDuodenalIntubation: Coding = {
  code: '24923-5',
  display: 'Small bowel Fluoroscopy Views Enteroclysis W contrast PO via duodenal intubation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SmallBowelFluoroscopyWContrastPO: Coding = {
  code: '24924-3',
  display: 'Small bowel Fluoroscopy W contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpinalArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24925-0',
  display: 'Spinal artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineUS: Coding = {
  code: '24926-8',
  display: 'Spine US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineFluoroscopyWContrastIntradisc: Coding = {
  code: '24927-6',
  display: 'Spine Fluoroscopy W contrast intradisc',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRayAPAndLateral: Coding = {
  code: '24928-4',
  display: 'Spine X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisWFlexionAndWExtension: Coding = {
  code: '24929-2',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosis: Coding = {
  code: '24930-0',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineFacetJoint: Coding = {
  code: '24931-8',
  display: 'Fluoroscopy Guidance for injection of Spine facet joint',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalCT: Coding = {
  code: '24932-6',
  display: 'Spine Cervical CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalCTWContrastIV: Coding = {
  code: '24933-4',
  display: 'Spine Cervical CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalCTWContrastIT: Coding = {
  code: '24934-2',
  display: 'Spine Cervical CT W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalMRI: Coding = {
  code: '24935-9',
  display: 'Spine Cervical MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalMRIWAnesthesia: Coding = {
  code: '24936-7',
  display: 'Spine Cervical MRI W anesthesia',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalMRIWAndWOContrastIV: Coding = {
  code: '24937-5',
  display: 'Spine Cervical MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalMRIWContrastIV: Coding = {
  code: '24938-3',
  display: 'Spine Cervical MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRay5Views: Coding = {
  code: '24939-1',
  display: 'Spine Cervical X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRaySingleView: Coding = {
  code: '24940-9',
  display: 'Spine Cervical X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRay3Views: Coding = {
  code: '24941-7',
  display: 'Spine Cervical X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPAndLateral: Coding = {
  code: '24942-5',
  display: 'Spine Cervical X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayLateral: Coding = {
  code: '24943-3',
  display: 'Spine Cervical X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRaySwimmers: Coding = {
  code: '24944-1',
  display: 'Spine Cervical X-ray Swimmers',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayWFlexionAndWExtension: Coding = {
  code: '24945-8',
  display: 'Spine Cervical X-ray W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRay: Coding = {
  code: '24946-6',
  display: 'Spine Cervical X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalFluoroscopyWContrastIT: Coding = {
  code: '24947-4',
  display: 'Spine Cervical Fluoroscopy W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalOdontoidAndCervicalAxisXRayAPSingleView: Coding = {
  code: '24948-2',
  display: 'Spine Cervical Odontoid and Cervical axis X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarCT: Coding = {
  code: '24963-1',
  display: 'Spine Lumbar CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarCTWContrastIV: Coding = {
  code: '24964-9',
  display: 'Spine Lumbar CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarCTWContrastIT: Coding = {
  code: '24965-6',
  display: 'Spine Lumbar CT W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarMRIWAndWOContrastIV: Coding = {
  code: '24967-2',
  display: 'Spine Lumbar MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarMRI: Coding = {
  code: '24968-0',
  display: 'Spine Lumbar MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayLateral: Coding = {
  code: '24969-8',
  display: 'Spine Lumbar X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPAndLateral: Coding = {
  code: '24970-6',
  display: 'Spine Lumbar X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayWFlexionAndWExtension: Coding = {
  code: '24971-4',
  display: 'Spine Lumbar X-ray W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRay: Coding = {
  code: '24972-2',
  display: 'Spine Lumbar X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForAspirationOfSpineLumbarSpace: Coding = {
  code: '24973-0',
  display: 'Fluoroscopy Guidance for aspiration of Spine Lumbar Space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarFluoroscopyWContrastIT: Coding = {
  code: '24974-8',
  display: 'Spine Lumbar Fluoroscopy W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarAndSacroiliacJointBilateralXRay: Coding = {
  code: '24975-5',
  display: 'Spine.lumbar and Sacroiliac joint - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarMRIWAnesthesia: Coding = {
  code: '24977-1',
  display: 'Spine Lumbar MRI W anesthesia',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicCT: Coding = {
  code: '24978-9',
  display: 'Spine Thoracic CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicCTWContrastIV: Coding = {
  code: '24979-7',
  display: 'Spine Thoracic CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicMRI: Coding = {
  code: '24980-5',
  display: 'Spine Thoracic MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicMRIWAndWOContrastIV: Coding = {
  code: '24981-3',
  display: 'Spine Thoracic MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicMRIWContrastIV: Coding = {
  code: '24982-1',
  display: 'Spine Thoracic MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRay: Coding = {
  code: '24983-9',
  display: 'Spine Thoracic X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRay2Views: Coding = {
  code: '24984-7',
  display: 'Spine Thoracic and Lumbar X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicFluoroscopyWContrastIT: Coding = {
  code: '24985-4',
  display: 'Spine Thoracic Fluoroscopy W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfSpine: Coding = {
  code: '24986-2',
  display: 'CT Guidance for biopsy of Spine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCTWContrastIV: Coding = {
  code: '24987-0',
  display: 'Spine CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpleenCT: Coding = {
  code: '24988-8',
  display: 'Spleen CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpleenCTWAndWOContrastIV: Coding = {
  code: '24989-6',
  display: 'Spleen CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpleenUS: Coding = {
  code: '24990-4',
  display: 'Spleen US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SplenicVeinAndPortalVeinFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24991-2',
  display: 'Splenic vein and Portal vein Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SplenicArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '24992-0',
  display: 'Splenic artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointsXRay: Coding = {
  code: '24993-8',
  display: 'Sternoclavicular Joints X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternumXRay: Coding = {
  code: '24994-6',
  display: 'Sternum X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPlacementOfTubeInStomach: Coding = {
  code: '24995-3',
  display: 'Fluoroscopy Guidance for placement of tube in Stomach',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForReplacementOfPercutaneousGastrostomyInStomach: Coding = {
  code: '24996-1',
  display: 'Fluoroscopy Guidance for replacement of percutaneous gastrostomy in Stomach',
  system: 'http://loinc.org',
};
const C80DocTypecodes_StomachScanForGastricEmptyingSolidPhaseWTc99mSCPO: Coding = {
  code: '24997-9',
  display: 'Stomach Scan for gastric emptying solid phase W Tc-99m SC PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlacementCheckOfGastrostomyTubeWContrastViaGITube: Coding = {
  code: '24998-7',
  display: 'Placement check of gastrostomy tube W contrast via GI tube',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointMRI: Coding = {
  code: '24999-5',
  display: 'Temporomandibular joint MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointXRay: Coding = {
  code: '25000-1',
  display: 'Temporomandibular joint X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleScanWTc99mPertechnetateIV: Coding = {
  code: '25001-9',
  display: 'Scrotum and Testicle Scan W Tc-99m pertechnetate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleUS: Coding = {
  code: '25002-7',
  display: 'Scrotum and Testicle US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighVesselsMRIAngiogramWContrastIV: Coding = {
  code: '25003-5',
  display: 'Thigh vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThreeVesselsFluoroscopicAngiogramWContrast: Coding = {
  code: '25005-0',
  display: 'Three vessels Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbXRay: Coding = {
  code: '25006-8',
  display: 'Thumb X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanWI131IV: Coding = {
  code: '25007-6',
  display: 'Thyroid Scan W I-131 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanAndUptakeWI131IV: Coding = {
  code: '25008-4',
  display: 'Thyroid Scan and uptake W I-131 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfThyroid: Coding = {
  code: '25009-2',
  display: 'US Guidance for biopsy of Thyroid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidUS: Coding = {
  code: '25010-0',
  display: 'Thyroid US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaAndFibulaXRay: Coding = {
  code: '25011-8',
  display: 'Tibia and Fibula X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibialArteryFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '25012-6',
  display: 'Tibial artery Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesXRay: Coding = {
  code: '25013-4',
  display: 'Toes X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TwoVesselsFluoroscopicAngiogramWContrast: Coding = {
  code: '25014-2',
  display: 'Two vessels Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperGITractReplacementOfPercutaneousGastrojejunostomy: Coding = {
  code: '25015-9',
  display: 'Upper GI tract Replacement of percutaneous gastrojejunostomy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrethraFluoroscopyWContrastIntraUrethra: Coding = {
  code: '25016-7',
  display: 'Urethra Fluoroscopy W contrast intra urethra',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrinaryBladderAndUrethraFluoroscopyWContrastIntraBladder: Coding = {
  code: '25017-5',
  display: 'Urinary Bladder and Urethra Fluoroscopy W contrast intra bladder',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrinaryBladderScan: Coding = {
  code: '25018-3',
  display: 'Urinary bladder Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrinaryBladderUS: Coding = {
  code: '25019-1',
  display: 'Urinary bladder US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrinaryBladderAndUrethraFluoroscopyWContrastRetrogradeViaUrethra: Coding = {
  code: '25020-9',
  display: 'Urinary Bladder and Urethra Fluoroscopy W contrast retrograde via urethra',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UterusAndFallopianTubesFluoroscopyWContrastIntrauterine: Coding = {
  code: '25022-5',
  display: 'Uterus and Fallopian tubes Fluoroscopy W contrast intrauterine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '25023-3',
  display: 'Vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfLongtermPeripheralCatheterInCentralVein: Coding = {
  code: '25024-1',
  display: 'Fluoroscopic angiogram Guidance for placement of longterm peripheral catheter in Central vein',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VenaCavaFluoroscopicAngiogramWContrastIV: Coding = {
  code: '25025-8',
  display: 'Vena cava Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfIVCFilterInInferiorVenaCavaWContrastIV: Coding = {
  code: '25026-6',
  display: 'Fluoroscopic angiogram Guidance for placement of IVC filter in Inferior vena cava-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GuidanceForPlacementOfLargeBoreCatheterIntoVesselInCentralVein: Coding = {
  code: '25027-4',
  display: 'Guidance for placement of large bore catheter into vessel in Central vein',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterForAdminstrationOfThrombolyticInVessel: Coding = {
  code: '25028-2',
  display: 'Fluoroscopic angiogram Guidance for placement of catheter for adminstration of thrombolytic in Vessel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterForVasoconstrictorInfusionInVessels: Coding = {
  code: '25029-0',
  display: 'Fluoroscopic angiogram Guidance for placement of catheter for vasoconstrictor infusion in Vessels',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalArteriesFluoroscopicAngiogramWContrastIA: Coding = {
  code: '25030-8',
  display: 'Abdominal Arteries Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScan: Coding = {
  code: '25031-6',
  display: 'Bone Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanWIn111TaggedWBCIV: Coding = {
  code: '25032-4',
  display: 'Bone Scan W In-111 tagged WBC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristMRI: Coding = {
  code: '25033-2',
  display: 'Wrist MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristFluoroscopyWContrastIntraarticular: Coding = {
  code: '25034-0',
  display: 'Wrist Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristMRIWAndWOContrastIV: Coding = {
  code: '25035-7',
  display: 'Wrist MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristUS: Coding = {
  code: '25036-5',
  display: 'Wrist US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCourtesyConsultation: Coding = {
  code: '25038-1',
  display: 'Unspecified body region Courtesy consultation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCTLimited: Coding = {
  code: '25039-9',
  display: 'Unspecified body region CT limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCT3D: Coding = {
  code: '25040-7',
  display: 'Unspecified body region CT 3D',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationOrBiopsyOfUnspecifiedBodyRegionWContrastIV: Coding = {
  code: '25041-5',
  display: 'CT Guidance for aspiration or biopsy of Unspecified body region-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationOrBiopsyOfUnspecifiedBodyRegion: Coding = {
  code: '25042-3',
  display: 'CT Guidance for aspiration or biopsy of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationOfUnspecifiedBodyRegion: Coding = {
  code: '25043-1',
  display: 'CT Guidance for aspiration of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfUnspecifiedBodyRegion: Coding = {
  code: '25044-9',
  display: 'CT Guidance for biopsy of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCT: Coding = {
  code: '25045-6',
  display: 'Unspecified body region CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCTWAnesthesia: Coding = {
  code: '25046-4',
  display: 'Unspecified body region CT W anesthesia',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCTWConsciousSedation: Coding = {
  code: '25047-2',
  display: 'Unspecified body region CT W conscious sedation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCT3DSagittalAndCoronalDisarticulation: Coding = {
  code: '25050-6',
  display: 'Unspecified body region CT 3D sagittal and coronal disarticulation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCTMultisectionalSagittal: Coding = {
  code: '25051-4',
  display: 'Unspecified body region CT Multisectional sagittal',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCTSagittalAndCoronal: Coding = {
  code: '25052-2',
  display: 'Unspecified body region CT sagittal and coronal',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForRadiosurgeryOfUnspecifiedBodyRegion: Coding = {
  code: '25053-0',
  display: 'CT Guidance for radiosurgery of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForRadiosurgeryOfUnspecifiedBodyRegionWContrastIV: Coding = {
  code: '25054-8',
  display: 'CT Guidance for radiosurgery of Unspecified body region-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionMRIAdditionalSequence: Coding = {
  code: '25055-5',
  display: 'Unspecified body region MRI additional sequence',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionMRI: Coding = {
  code: '25056-3',
  display: 'Unspecified body region MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionMRIWConsciousSedation: Coding = {
  code: '25057-1',
  display: 'Unspecified body region MRI W conscious sedation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionMRIAngiogramWContrastIV: Coding = {
  code: '25058-9',
  display: 'Unspecified body region MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfUnspecifiedBodyRegion: Coding = {
  code: '25059-7',
  display: 'US Guidance for biopsy of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionUSNoCharge: Coding = {
  code: '25060-5',
  display: 'Unspecified body region US No charge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionUS: Coding = {
  code: '25061-3',
  display: 'Unspecified body region US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionXRayComparisonView: Coding = {
  code: '25062-1',
  display: 'Unspecified body region X-ray Comparison view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VesselFluoroscopicAngiogramSingleViewWContrastIA: Coding = {
  code: '25063-9',
  display: 'Vessel Fluoroscopic angiogram Single view W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VesselFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '25064-7',
  display: 'Vessel Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy15Minutes: Coding = {
  code: '25065-4',
  display: 'Unspecified body region Fluoroscopy 15 minutes',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy30Minutes: Coding = {
  code: '25066-2',
  display: 'Unspecified body region Fluoroscopy 30 minutes',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy45Minutes: Coding = {
  code: '25067-0',
  display: 'Unspecified body region Fluoroscopy 45 minutes',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy1Hour: Coding = {
  code: '25068-8',
  display: 'Unspecified body region Fluoroscopy 1 hour',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfUnspecifiedBodyRegion: Coding = {
  code: '25069-6',
  display: 'Fluoroscopy Guidance for biopsy of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyDuringSurgery: Coding = {
  code: '25070-4',
  display: 'Unspecified body region Fluoroscopy during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionXRayTomograph: Coding = {
  code: '25071-2',
  display: 'Unspecified body region X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GuidanceForPlacementOfInfusionPortInUnspecifiedBodyRegion: Coding = {
  code: '25072-0',
  display: 'Guidance for placement of infusion port in Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VesselFluoroscopicAngiogramRemovalOfForeignBodyFromVascularSpace: Coding = {
  code: '25073-8',
  display: 'Vessel Fluoroscopic angiogram Removal of foreign body from vascular space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ZygomaticArchXRay: Coding = {
  code: '25074-6',
  display: 'Zygomatic arch X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HepaticArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '25076-1',
  display: 'Hepatic artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInHepaticArteryWContrastIA: Coding = {
  code: '25077-9',
  display: 'Fluoroscopic angiogram Guidance for placement of catheter in Hepatic artery-- W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPlacementOfStentInIntrahepaticPortalSystem: Coding = {
  code: '25078-7',
  display: 'Fluoroscopy Guidance for placement of stent in Intrahepatic portal system',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyArteriesFluoroscopicAngiogramWContrastIA: Coding = {
  code: '25079-5',
  display: 'Kidney arteries Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVeinBilateralFluoroscopicAngiogramWContrastIVAndWReninSampling: Coding = {
  code: '25080-3',
  display: 'Renal vein - bilateral Fluoroscopic angiogram W contrast IV and W renin sampling',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '25081-1',
  display: 'Renal vessel Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMolesVolumeInSerumOrPlasma: Coding = {
  code: '25561-2',
  display: '11-Deoxycorticosterone [Moles/volume] in Serum or Plasma',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinBilateralFluoroscopicAngiogramWContrastIV: Coding = {
  code: '26064-6',
  display: 'Vein - bilateral Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinLeftFluoroscopicAngiogramWContrastIV: Coding = {
  code: '26065-3',
  display: 'Vein - left Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinRightFluoroscopicAngiogramWContrastIV: Coding = {
  code: '26066-1',
  display: 'Vein - right Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SalivaryGlandBilateralFluoroscopyWContrastIntraSalivaryDuct: Coding = {
  code: '26067-9',
  display: 'Salivary gland - bilateral Fluoroscopy W contrast intra salivary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SalivaryGlandLeftFluoroscopyWContrastIntraSalivaryDuct: Coding = {
  code: '26068-7',
  display: 'Salivary gland - left Fluoroscopy W contrast intra salivary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SalivaryGlandRightFluoroscopyWContrastIntraSalivaryDuct: Coding = {
  code: '26069-5',
  display: 'Salivary gland - right Fluoroscopy W contrast intra salivary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralFluoroscopyWContrastIntraarticular: Coding = {
  code: '26070-3',
  display: 'Hip - bilateral Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftFluoroscopyWContrastIntraarticular: Coding = {
  code: '26071-1',
  display: 'Hip - left Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightFluoroscopyWContrastIntraarticular: Coding = {
  code: '26072-9',
  display: 'Hip - right Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralFluoroscopyWContrastIntraarticular: Coding = {
  code: '26073-7',
  display: 'Knee - bilateral Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftFluoroscopyWContrastIntraarticular: Coding = {
  code: '26074-5',
  display: 'Knee - left Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightFluoroscopyWContrastIntraarticular: Coding = {
  code: '26075-2',
  display: 'Knee - right Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralFluoroscopyWContrastIntraarticular: Coding = {
  code: '26076-0',
  display: 'Shoulder - bilateral Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftFluoroscopyWContrastIntraarticular: Coding = {
  code: '26077-8',
  display: 'Shoulder - left Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightFluoroscopyWContrastIntraarticular: Coding = {
  code: '26078-6',
  display: 'Shoulder - right Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '26079-4',
  display: 'Carotid artery - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '26080-2',
  display: 'Carotid artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '26081-0',
  display: 'Carotid artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpinalArteryBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '26082-8',
  display: 'Spinal artery - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpinalArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '26083-6',
  display: 'Spinal artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpinalArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '26084-4',
  display: 'Spinal artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayStanding: Coding = {
  code: '26085-1',
  display: 'Knee - bilateral X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayStanding: Coding = {
  code: '26086-9',
  display: 'Knee - left X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayStanding: Coding = {
  code: '26087-7',
  display: 'Knee - right X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralScan: Coding = {
  code: '26088-5',
  display: 'Knee - bilateral Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftScan: Coding = {
  code: '26089-3',
  display: 'Knee - left Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightScan: Coding = {
  code: '26090-1',
  display: 'Knee - right Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleBilateralScanWTc99mPertechnetateIV: Coding = {
  code: '26091-9',
  display: 'Scrotum and Testicle - bilateral Scan W Tc-99m pertechnetate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleLeftScanWTc99mPertechnetateIV: Coding = {
  code: '26092-7',
  display: 'Scrotum and Testicle - left Scan W Tc-99m pertechnetate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleRightScanWTc99mPertechnetateIV: Coding = {
  code: '26093-5',
  display: 'Scrotum and Testicle - right Scan W Tc-99m pertechnetate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralXRayStanding: Coding = {
  code: '26094-3',
  display: 'Foot - bilateral X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRayStanding: Coding = {
  code: '26095-0',
  display: 'Foot - left X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRayStanding: Coding = {
  code: '26096-8',
  display: 'Foot - right X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRay: Coding = {
  code: '26097-6',
  display: 'Ankle - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRay: Coding = {
  code: '26098-4',
  display: 'Ankle - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRay: Coding = {
  code: '26099-2',
  display: 'Ankle - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusBilateralXRay: Coding = {
  code: '26100-8',
  display: 'Calcaneus - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusLeftXRay: Coding = {
  code: '26101-6',
  display: 'Calcaneus - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusRightXRay: Coding = {
  code: '26102-4',
  display: 'Calcaneus - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleBilateralXRay: Coding = {
  code: '26106-5',
  display: 'Clavicle - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleLeftXRay: Coding = {
  code: '26107-3',
  display: 'Clavicle - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleRightXRay: Coding = {
  code: '26108-1',
  display: 'Clavicle - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralXRay: Coding = {
  code: '26109-9',
  display: 'Elbow - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRay: Coding = {
  code: '26110-7',
  display: 'Elbow - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRay: Coding = {
  code: '26111-5',
  display: 'Elbow - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityBilateralXRay: Coding = {
  code: '26112-3',
  display: 'Lower extremity - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftXRay: Coding = {
  code: '26113-1',
  display: 'Lower extremity - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightXRay: Coding = {
  code: '26114-9',
  display: 'Lower extremity - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityBilateralXRay: Coding = {
  code: '26115-6',
  display: 'Upper extremity - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityLeftXRay: Coding = {
  code: '26116-4',
  display: 'Upper extremity - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityRightXRay: Coding = {
  code: '26117-2',
  display: 'Upper extremity - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurBilateralXRay: Coding = {
  code: '26118-0',
  display: 'Femur - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurLeftXRay: Coding = {
  code: '26120-6',
  display: 'Femur - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightXRay: Coding = {
  code: '26122-2',
  display: 'Femur - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerBilateralXRay: Coding = {
  code: '26124-8',
  display: 'Finger - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerLeftXRay: Coding = {
  code: '26125-5',
  display: 'Finger - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerRightXRay: Coding = {
  code: '26126-3',
  display: 'Finger - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralXRay: Coding = {
  code: '26127-1',
  display: 'Foot - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRay: Coding = {
  code: '26128-9',
  display: 'Foot - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRay: Coding = {
  code: '26129-7',
  display: 'Foot - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralXRay: Coding = {
  code: '26130-5',
  display: 'Hip - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRay: Coding = {
  code: '26131-3',
  display: 'Hip - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRay: Coding = {
  code: '26132-1',
  display: 'Hip - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcetabulumBilateralXRay: Coding = {
  code: '26133-9',
  display: 'Acetabulum - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcetabulumLeftXRay: Coding = {
  code: '26134-7',
  display: 'Acetabulum - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcetabulumRightXRay: Coding = {
  code: '26135-4',
  display: 'Acetabulum - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointBilateralXRay: Coding = {
  code: '26136-2',
  display: 'Acromioclavicular joint - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointLeftXRay: Coding = {
  code: '26137-0',
  display: 'Acromioclavicular joint - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointRightXRay: Coding = {
  code: '26138-8',
  display: 'Acromioclavicular joint - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidBilateralXRay: Coding = {
  code: '26139-6',
  display: 'Mastoid - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidLeftXRay: Coding = {
  code: '26140-4',
  display: 'Mastoid - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidRightXRay: Coding = {
  code: '26141-2',
  display: 'Mastoid - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OpticForamenBilateralXRay: Coding = {
  code: '26142-0',
  display: 'Optic foramen - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OpticForamenLeftXRay: Coding = {
  code: '26143-8',
  display: 'Optic foramen - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OpticForamenRightXRay: Coding = {
  code: '26144-6',
  display: 'Optic foramen - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusBilateralAndUlnaBilateralXRay: Coding = {
  code: '26146-1',
  display: 'Radius - bilateral and Ulna - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusLeftAndUlnaLeftXRay: Coding = {
  code: '26148-7',
  display: 'Radius - left and Ulna.left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusRightAndUlnaRightXRay: Coding = {
  code: '26150-3',
  display: 'Radius - right and Ulna - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsBilateralXRay: Coding = {
  code: '26151-1',
  display: 'Ribs - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLeftXRay: Coding = {
  code: '26152-9',
  display: 'Ribs - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsRightXRay: Coding = {
  code: '26153-7',
  display: 'Ribs - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaBilateralXRay: Coding = {
  code: '26154-5',
  display: 'Scapula - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaLeftXRay: Coding = {
  code: '26155-2',
  display: 'Scapula - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaRightXRay: Coding = {
  code: '26156-0',
  display: 'Scapula - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRay: Coding = {
  code: '26157-8',
  display: 'Shoulder - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRay: Coding = {
  code: '26158-6',
  display: 'Shoulder - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRay: Coding = {
  code: '26159-4',
  display: 'Shoulder - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbBilateralXRay: Coding = {
  code: '26160-2',
  display: 'Thumb - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbLeftXRay: Coding = {
  code: '26161-0',
  display: 'Thumb - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbRightXRay: Coding = {
  code: '26162-8',
  display: 'Thumb - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaBilateralAndFibulaBilateralXRay: Coding = {
  code: '26163-6',
  display: 'Tibia - bilateral and Fibula - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaLeftAndFibulaLeftXRay: Coding = {
  code: '26164-4',
  display: 'Tibia - left and Fibula - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaRightAndFibulaRightXRay: Coding = {
  code: '26165-1',
  display: 'Tibia - right and Fibula - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesBilateralXRay: Coding = {
  code: '26166-9',
  display: 'Toes - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesLeftXRay: Coding = {
  code: '26167-7',
  display: 'Toes - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesRightXRay: Coding = {
  code: '26168-5',
  display: 'Toes - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRay: Coding = {
  code: '26169-3',
  display: 'Wrist - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRay: Coding = {
  code: '26170-1',
  display: 'Wrist - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRay: Coding = {
  code: '26171-9',
  display: 'Wrist - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ZygomaticArchBilateralXRay: Coding = {
  code: '26172-7',
  display: 'Zygomatic arch - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ZygomaticArchLeftXRay: Coding = {
  code: '26173-5',
  display: 'Zygomatic arch - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ZygomaticArchRightXRay: Coding = {
  code: '26174-3',
  display: 'Zygomatic arch - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramScreening: Coding = {
  code: '26175-0',
  display: 'Breast - bilateral Mammogram screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramScreening: Coding = {
  code: '26176-8',
  display: 'Breast - left Mammogram screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogramScreening: Coding = {
  code: '26177-6',
  display: 'Breast - right Mammogram screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralArteryBilateralFluoroscopicAngiogramRunoffWContrastIA: Coding = {
  code: '26178-4',
  display: 'Femoral artery - bilateral Fluoroscopic angiogram runoff W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralArteryLeftFluoroscopicAngiogramRunoffWContrastIA: Coding = {
  code: '26179-2',
  display: 'Femoral artery - left Fluoroscopic angiogram runoff W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralArteryRightFluoroscopicAngiogramRunoffWContrastIA: Coding = {
  code: '26180-0',
  display: 'Femoral artery - right Fluoroscopic angiogram runoff W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletVesselsBilateralMRIAngiogramWContrastIV: Coding = {
  code: '26181-8',
  display: 'Thoracic outlet vessels - bilateral MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletVesselsLeftMRIAngiogramWContrastIV: Coding = {
  code: '26182-6',
  display: 'Thoracic outlet vessels - left MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletVesselsRightMRIAngiogramWContrastIV: Coding = {
  code: '26183-4',
  display: 'Thoracic outlet vessels - right MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityBilateralCTWContrastIV: Coding = {
  code: '26184-2',
  display: 'Extremity - bilateral CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityLeftCTWContrastIV: Coding = {
  code: '26185-9',
  display: 'Extremity - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityRightCTWContrastIV: Coding = {
  code: '26186-7',
  display: 'Extremity - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralMRIWAndWOContrastIV: Coding = {
  code: '26187-5',
  display: 'Ankle - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftMRIWAndWOContrastIV: Coding = {
  code: '26188-3',
  display: 'Ankle - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightMRIWAndWOContrastIV: Coding = {
  code: '26189-1',
  display: 'Ankle - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletBilateralMRIWAndWOContrastIV: Coding = {
  code: '26190-9',
  display: 'Thoracic outlet - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletLeftMRIWAndWOContrastIV: Coding = {
  code: '26191-7',
  display: 'Thoracic outlet - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletRightMRIWAndWOContrastIV: Coding = {
  code: '26192-5',
  display: 'Thoracic outlet - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralMRIWAndWOContrastIV: Coding = {
  code: '26193-3',
  display: 'Elbow - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftMRIWAndWOContrastIV: Coding = {
  code: '26194-1',
  display: 'Elbow - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightMRIWAndWOContrastIV: Coding = {
  code: '26195-8',
  display: 'Elbow - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighBilateralMRIWAndWOContrastIV: Coding = {
  code: '26196-6',
  display: 'Thigh - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighLeftMRIWAndWOContrastIV: Coding = {
  code: '26197-4',
  display: 'Thigh - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighRightMRIWAndWOContrastIV: Coding = {
  code: '26198-2',
  display: 'Thigh - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralMRIWAndWOContrastIV: Coding = {
  code: '26199-0',
  display: 'Knee - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftMRIWAndWOContrastIV: Coding = {
  code: '26200-6',
  display: 'Knee - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightMRIWAndWOContrastIV: Coding = {
  code: '26201-4',
  display: 'Knee - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralMRIWAndWOContrastIV: Coding = {
  code: '26202-2',
  display: 'Shoulder - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftMRIWAndWOContrastIV: Coding = {
  code: '26203-0',
  display: 'Shoulder - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightMRIWAndWOContrastIV: Coding = {
  code: '26204-8',
  display: 'Shoulder - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralMRIWAndWOContrastIV: Coding = {
  code: '26205-5',
  display: 'Wrist - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftMRIWAndWOContrastIV: Coding = {
  code: '26206-3',
  display: 'Wrist - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightMRIWAndWOContrastIV: Coding = {
  code: '26207-1',
  display: 'Wrist - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralMRI: Coding = {
  code: '26208-9',
  display: 'Ankle - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftMRI: Coding = {
  code: '26209-7',
  display: 'Ankle - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightMRI: Coding = {
  code: '26210-5',
  display: 'Ankle - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletBilateralMRI: Coding = {
  code: '26211-3',
  display: 'Thoracic outlet - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletLeftMRI: Coding = {
  code: '26212-1',
  display: 'Thoracic outlet - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletRightMRI: Coding = {
  code: '26213-9',
  display: 'Thoracic outlet - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralUS: Coding = {
  code: '26214-7',
  display: 'Breast - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftUS: Coding = {
  code: '26215-4',
  display: 'Breast - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightUS: Coding = {
  code: '26216-2',
  display: 'Breast - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryBilateralUS: Coding = {
  code: '26217-0',
  display: 'Carotid artery - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryLeftUS: Coding = {
  code: '26218-8',
  display: 'Carotid artery - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryRightUS: Coding = {
  code: '26219-6',
  display: 'Carotid artery - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralMRI: Coding = {
  code: '26220-4',
  display: 'Elbow - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftMRI: Coding = {
  code: '26221-2',
  display: 'Elbow - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightMRI: Coding = {
  code: '26222-0',
  display: 'Elbow - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityBilateralUS: Coding = {
  code: '26223-8',
  display: 'Extremity - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityBilateralCT: Coding = {
  code: '26224-6',
  display: 'Extremity - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityLeftUS: Coding = {
  code: '26225-3',
  display: 'Extremity - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityLeftCT: Coding = {
  code: '26226-1',
  display: 'Extremity - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointBilateralMRI: Coding = {
  code: '26227-9',
  display: 'Lower extremity joint - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointLeftMRI: Coding = {
  code: '26228-7',
  display: 'Lower extremity joint - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointRightMRI: Coding = {
  code: '26229-5',
  display: 'Lower extremity joint - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityRightUS: Coding = {
  code: '26230-3',
  display: 'Extremity - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityRightCT: Coding = {
  code: '26231-1',
  display: 'Extremity - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityBilateralMRI: Coding = {
  code: '26232-9',
  display: 'Upper extremity - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityLeftMRI: Coding = {
  code: '26233-7',
  display: 'Upper extremity - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityRightMRI: Coding = {
  code: '26234-5',
  display: 'Upper extremity - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighBilateralMRI: Coding = {
  code: '26235-2',
  display: 'Thigh - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighLeftMRI: Coding = {
  code: '26236-0',
  display: 'Thigh - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighRightMRI: Coding = {
  code: '26237-8',
  display: 'Thigh - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerBilateralMRI: Coding = {
  code: '26238-6',
  display: 'Finger - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerLeftMRI: Coding = {
  code: '26239-4',
  display: 'Finger - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerRightMRI: Coding = {
  code: '26240-2',
  display: 'Finger - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralMRI: Coding = {
  code: '26241-0',
  display: 'Foot - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftMRI: Coding = {
  code: '26242-8',
  display: 'Foot - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightMRI: Coding = {
  code: '26243-6',
  display: 'Foot - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmBilateralMRI: Coding = {
  code: '26244-4',
  display: 'Forearm - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmLeftMRI: Coding = {
  code: '26245-1',
  display: 'Forearm - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmRightMRI: Coding = {
  code: '26246-9',
  display: 'Forearm - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralMRI: Coding = {
  code: '26247-7',
  display: 'Hand - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftMRI: Coding = {
  code: '26248-5',
  display: 'Hand - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightMRI: Coding = {
  code: '26249-3',
  display: 'Hand - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralUS: Coding = {
  code: '26250-1',
  display: 'Hip - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftUS: Coding = {
  code: '26251-9',
  display: 'Hip - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightUS: Coding = {
  code: '26252-7',
  display: 'Hip - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalBilateralXRayTomograph: Coding = {
  code: '26253-5',
  display: 'Internal auditory canal - bilateral X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalLeftXRayTomograph: Coding = {
  code: '26254-3',
  display: 'Internal auditory canal - left X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalRightXRayTomograph: Coding = {
  code: '26255-0',
  display: 'Internal auditory canal - right X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralMRI: Coding = {
  code: '26256-8',
  display: 'Knee - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftMRI: Coding = {
  code: '26257-6',
  display: 'Knee - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightMRI: Coding = {
  code: '26258-4',
  display: 'Knee - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipBilateralMRI: Coding = {
  code: '26259-2',
  display: 'Pelvis and Hip - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipLeftMRI: Coding = {
  code: '26260-0',
  display: 'Pelvis and Hip - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipRightMRI: Coding = {
  code: '26261-8',
  display: 'Pelvis and Hip - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PoplitealSpaceBilateralUS: Coding = {
  code: '26262-6',
  display: 'Popliteal space - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PoplitealSpaceLeftUS: Coding = {
  code: '26263-4',
  display: 'Popliteal space - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PoplitealSpaceRightUS: Coding = {
  code: '26264-2',
  display: 'Popliteal space - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralUS: Coding = {
  code: '26265-9',
  display: 'Shoulder - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralMRI: Coding = {
  code: '26266-7',
  display: 'Shoulder - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftUS: Coding = {
  code: '26267-5',
  display: 'Shoulder - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftMRI: Coding = {
  code: '26268-3',
  display: 'Shoulder - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightUS: Coding = {
  code: '26269-1',
  display: 'Shoulder - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightMRI: Coding = {
  code: '26270-9',
  display: 'Shoulder - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleBilateralUS: Coding = {
  code: '26271-7',
  display: 'Scrotum and Testicle - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleLeftUS: Coding = {
  code: '26272-5',
  display: 'Scrotum and Testicle - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleRightUS: Coding = {
  code: '26273-3',
  display: 'Scrotum and Testicle - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralMRI: Coding = {
  code: '26277-4',
  display: 'Wrist - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralUS: Coding = {
  code: '26278-2',
  display: 'Wrist - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftMRI: Coding = {
  code: '26279-0',
  display: 'Wrist - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftUS: Coding = {
  code: '26280-8',
  display: 'Wrist - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightMRI: Coding = {
  code: '26281-6',
  display: 'Wrist - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightUS: Coding = {
  code: '26282-4',
  display: 'Wrist - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayMerchants: Coding = {
  code: '26283-2',
  display: 'Knee - bilateral X-ray Merchants',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayMerchants: Coding = {
  code: '26284-0',
  display: 'Knee - left X-ray Merchants',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayMerchants: Coding = {
  code: '26285-7',
  display: 'Knee - right X-ray Merchants',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralUSLimited: Coding = {
  code: '26286-5',
  display: 'Breast - bilateral US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramLimited: Coding = {
  code: '26287-3',
  display: 'Breast - bilateral Mammogram limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftUSLimited: Coding = {
  code: '26288-1',
  display: 'Breast - left US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramLimited: Coding = {
  code: '26289-9',
  display: 'Breast - left Mammogram limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightUSLimited: Coding = {
  code: '26290-7',
  display: 'Breast - right US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogramLimited: Coding = {
  code: '26291-5',
  display: 'Breast - right Mammogram limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceStereotacticForBiopsyOfBreastBilateral: Coding = {
  code: '26292-3',
  display: 'Mammogram Guidance.stereotactic for biopsy of Breast - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceStereotacticForBiopsyOfBreastLeft: Coding = {
  code: '26293-1',
  display: 'Mammogram Guidance.stereotactic for biopsy of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceStereotacticForBiopsyOfBreastRight: Coding = {
  code: '26294-9',
  display: 'Mammogram Guidance.stereotactic for biopsy of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForRepositionOfCatheterInCentralVeinBilateralWContrastIV: Coding = {
  code: '26295-6',
  display: 'Fluoroscopic angiogram Guidance for reposition of catheter in Central vein - bilateral-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForRepositionOfCatheterInCentralVeinLeftWContrastIV: Coding = {
  code: '26296-4',
  display: 'Fluoroscopic angiogram Guidance for reposition of catheter in Central vein - left-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForRepositionOfCatheterInCentralVeinRightWContrastIV: Coding = {
  code: '26297-2',
  display: 'Fluoroscopic angiogram Guidance for reposition of catheter in Central vein - right-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForAtherectomyOfVeinBilateralWContrastIV: Coding = {
  code: '26298-0',
  display: 'Fluoroscopic angiogram Guidance for atherectomy of Vein - bilateral-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForAtherectomyOfVeinLeftWContrastIV: Coding = {
  code: '26299-8',
  display: 'Fluoroscopic angiogram Guidance for atherectomy of Vein - left-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForAtherectomyOfVeinRightWContrastIV: Coding = {
  code: '26300-4',
  display: 'Fluoroscopic angiogram Guidance for atherectomy of Vein - right-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInVeinBilateral: Coding = {
  code: '26301-2',
  display: 'Fluoroscopic angiogram Guidance for placement of stent in Vein - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInVeinLeft: Coding = {
  code: '26302-0',
  display: 'Fluoroscopic angiogram Guidance for placement of stent in Vein - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInVeinRight: Coding = {
  code: '26303-8',
  display: 'Fluoroscopic angiogram Guidance for placement of stent in Vein - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfLongtermPeripheralCatheterInCentralVeinBilateral: Coding = {
  code: '26304-6',
  display: 'Fluoroscopic angiogram Guidance for placement of longterm peripheral catheter in Central vein - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfLongtermPeripheralCatheterInCentralVeinLeft: Coding = {
  code: '26305-3',
  display: 'Fluoroscopic angiogram Guidance for placement of longterm peripheral catheter in Central vein - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfLongtermPeripheralCatheterInCentralVeinRight: Coding = {
  code: '26306-1',
  display: 'Fluoroscopic angiogram Guidance for placement of longterm peripheral catheter in Central vein - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GuidanceForPlacementOfLargeBoreCatheterIntoVesselInCentralVeinBilateral: Coding = {
  code: '26307-9',
  display: 'Guidance for placement of large bore catheter into vessel in Central vein - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GuidanceForPlacementOfLargeBoreCatheterIntoVesselInCentralVeinLeft: Coding = {
  code: '26308-7',
  display: 'Guidance for placement of large bore catheter into vessel in Central vein - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GuidanceForPlacementOfLargeBoreCatheterIntoVesselInCentralVeinRight: Coding = {
  code: '26309-5',
  display: 'Guidance for placement of large bore catheter into vessel in Central vein - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInCentralVeinBilateralWContrastIV: Coding = {
  code: '26310-3',
  display: 'Fluoroscopic angiogram Guidance for placement of catheter in Central vein - bilateral-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInCentralVeinLeftWContrastIV: Coding = {
  code: '26311-1',
  display: 'Fluoroscopic angiogram Guidance for placement of catheter in Central vein - left-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInCentralVeinRightWContrastIV: Coding = {
  code: '26312-9',
  display: 'Fluoroscopic angiogram Guidance for placement of catheter in Central vein - right-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleLocalizationOfBreastBilateral: Coding = {
  code: '26313-7',
  display: 'US Guidance for needle localization of Breast - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleLocalizationOfBreastLeft: Coding = {
  code: '26314-5',
  display: 'US Guidance for needle localization of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForNeedleLocalizationOfMassOfBreastBilateral: Coding = {
  code: '26315-2',
  display: 'Mammogram Guidance for needle localization of mass of Breast - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForNeedleLocalizationOfMassOfBreastLeft: Coding = {
  code: '26316-0',
  display: 'Mammogram Guidance for needle localization of mass of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForNeedleLocalizationOfMassOfBreastRight: Coding = {
  code: '26317-8',
  display: 'Mammogram Guidance for needle localization of mass of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleLocalizationOfBreastRight: Coding = {
  code: '26318-6',
  display: 'US Guidance for needle localization of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForInjectionOfSacroiliacJointBilateral: Coding = {
  code: '26319-4',
  display: 'CT Guidance for injection of Sacroiliac joint - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForInjectionOfSacroiliacJointLeft: Coding = {
  code: '26320-2',
  display: 'CT Guidance for injection of Sacroiliac joint - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForInjectionOfSacroiliacJointRight: Coding = {
  code: '26321-0',
  display: 'CT Guidance for injection of Sacroiliac joint - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineFacetJointBilateral: Coding = {
  code: '26322-8',
  display: 'Fluoroscopy Guidance for injection of Spine facet joint - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineFacetJointLeft: Coding = {
  code: '26323-6',
  display: 'Fluoroscopy Guidance for injection of Spine facet joint - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineFacetJointRight: Coding = {
  code: '26324-4',
  display: 'Fluoroscopy Guidance for injection of Spine facet joint - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfExtremityBilateral: Coding = {
  code: '26325-1',
  display: 'US Guidance for drainage of Extremity - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfExtremityLeft: Coding = {
  code: '26326-9',
  display: 'US Guidance for drainage of Extremity - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfExtremityRight: Coding = {
  code: '26327-7',
  display: 'US Guidance for drainage of Extremity - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfKidneyBilateral: Coding = {
  code: '26328-5',
  display: 'US Guidance for drainage of Kidney - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfKidneyLeft: Coding = {
  code: '26329-3',
  display: 'US Guidance for drainage of Kidney - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfKidneyRight: Coding = {
  code: '26330-1',
  display: 'US Guidance for drainage of Kidney - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForChangeOfCentralCatheterInCentralVeinBilateralWContrastIV: Coding = {
  code: '26331-9',
  display: 'Fluoroscopic angiogram Guidance for change of central catheter in Central vein - bilateral-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForChangeOfCentralCatheterInCentralVeinLeftWContrastIV: Coding = {
  code: '26332-7',
  display: 'Fluoroscopic angiogram Guidance for change of central catheter in Central vein - left-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForChangeOfCentralCatheterInCentralVeinRightWContrastIV: Coding = {
  code: '26333-5',
  display: 'Fluoroscopic angiogram Guidance for change of central catheter in Central vein - right-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForCoreNeedlePercutaneousBiopsyOfBreastBilateral: Coding = {
  code: '26334-3',
  display: 'Mammogram Guidance for core needle percutaneous biopsy of Breast - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForCoreNeedlePercutaneousBiopsyOfBreastLeft: Coding = {
  code: '26335-0',
  display: 'Mammogram Guidance for core needle percutaneous biopsy of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForCoreNeedlePercutaneousBiopsyOfBreastRight: Coding = {
  code: '26336-8',
  display: 'Mammogram Guidance for core needle percutaneous biopsy of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForBiopsyOfBreastBilateral: Coding = {
  code: '26337-6',
  display: 'Mammogram Guidance for biopsy of Breast - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForBiopsyOfBreastLeft: Coding = {
  code: '26338-4',
  display: 'Mammogram Guidance for biopsy of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForBiopsyOfBreastRight: Coding = {
  code: '26339-2',
  display: 'Mammogram Guidance for biopsy of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfKidneyBilateral: Coding = {
  code: '26340-0',
  display: 'US Guidance for biopsy of Kidney - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfKidneyLeft: Coding = {
  code: '26341-8',
  display: 'US Guidance for biopsy of Kidney - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfKidneyRight: Coding = {
  code: '26342-6',
  display: 'US Guidance for biopsy of Kidney - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForAspirationOfCystOfBreastBilateral: Coding = {
  code: '26343-4',
  display: 'Mammogram Guidance for aspiration of cyst of Breast - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForAspirationOfCystOfBreastLeft: Coding = {
  code: '26344-2',
  display: 'Mammogram Guidance for aspiration of cyst of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForAspirationOfCystOfBreastRight: Coding = {
  code: '26345-9',
  display: 'Mammogram Guidance for aspiration of cyst of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramDiagnostic: Coding = {
  code: '26346-7',
  display: 'Breast - bilateral Mammogram diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramDiagnostic: Coding = {
  code: '26347-5',
  display: 'Breast - left Mammogram diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogramDiagnostic: Coding = {
  code: '26348-3',
  display: 'Breast - right Mammogram diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramDiagnosticLimited: Coding = {
  code: '26349-1',
  display: 'Breast - bilateral Mammogram diagnostic limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramDiagnosticLimited: Coding = {
  code: '26350-9',
  display: 'Breast - left Mammogram diagnostic limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogramDiagnosticLimited: Coding = {
  code: '26351-7',
  display: 'Breast - right Mammogram diagnostic limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralAndHandBilateralXRayBoneAge: Coding = {
  code: '26352-5',
  display: 'Wrist - bilateral and Hand - bilateral X-ray bone age',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftAndHandLeftXRayBoneAge: Coding = {
  code: '26353-3',
  display: 'Wrist - left and Hand - left X-ray bone age',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightAndHandRightXRayBoneAge: Coding = {
  code: '26354-1',
  display: 'Wrist - right and Hand - right X-ray bone age',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralXRayArthritis: Coding = {
  code: '26355-8',
  display: 'Hand - bilateral X-ray arthritis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRayArthritis: Coding = {
  code: '26356-6',
  display: 'Hand - left X-ray arthritis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRayArthritis: Coding = {
  code: '26357-4',
  display: 'Hand - right X-ray arthritis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayAPSingleViewStanding: Coding = {
  code: '26358-2',
  display: 'Knee - bilateral X-ray AP single view standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayAPSingleViewStanding: Coding = {
  code: '26359-0',
  display: 'Knee - left X-ray AP single view standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAPSingleViewStanding: Coding = {
  code: '26360-8',
  display: 'Knee - right X-ray AP single view standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayAPAndPAStanding: Coding = {
  code: '26361-6',
  display: 'Knee - bilateral X-ray AP and PA standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayAPAndPAStanding: Coding = {
  code: '26362-4',
  display: 'Knee - left X-ray AP and PA standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAPAndPAStanding: Coding = {
  code: '26363-2',
  display: 'Knee - right X-ray AP and PA standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayAPAndLateralStanding: Coding = {
  code: '26364-0',
  display: 'Knee - bilateral X-ray AP and lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayAPAndLateralStanding: Coding = {
  code: '26365-7',
  display: 'Knee - left X-ray AP and lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAPAndLateralStanding: Coding = {
  code: '26366-5',
  display: 'Knee - right X-ray AP and lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrachiocephalicArteryLeftFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '26368-1',
  display: 'Brachiocephalic artery - left Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrachiocephalicArteryRightFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '26369-9',
  display: 'Brachiocephalic artery - right Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacArteryBilateralFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '26370-7',
  display: 'Iliac artery - bilateral Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacArteryLeftFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '26371-5',
  display: 'Iliac artery - left Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacArteryRightFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '26372-3',
  display: 'Iliac artery - right Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibialArteryBilateralFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '26373-1',
  display: 'Tibial artery - bilateral Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibialArteryLeftFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '26374-9',
  display: 'Tibial artery - left Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibialArteryRightFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '26375-6',
  display: 'Tibial artery - right Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdministrationOfVasodilatorIntoCatheterOfVeinBilateral: Coding = {
  code: '26376-4',
  display: 'Administration of vasodilator into catheter of Vein - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdministrationOfVasodilatorIntoCatheterOfVeinLeft: Coding = {
  code: '26377-2',
  display: 'Administration of vasodilator into catheter of Vein - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdministrationOfVasodilatorIntoCatheterOfVeinRight: Coding = {
  code: '26378-0',
  display: 'Administration of vasodilator into catheter of Vein - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralXRay3Views: Coding = {
  code: '26379-8',
  display: 'Hand - bilateral X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRay3Views: Coding = {
  code: '26380-6',
  display: 'Hand - left X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRay3Views: Coding = {
  code: '26381-4',
  display: 'Hand - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRay3Views: Coding = {
  code: '26382-2',
  display: 'Shoulder - bilateral X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRay3Views: Coding = {
  code: '26383-0',
  display: 'Shoulder - left X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRay3Views: Coding = {
  code: '26384-8',
  display: 'Shoulder - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRay2Views: Coding = {
  code: '26385-5',
  display: 'Ankle - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRay2Views: Coding = {
  code: '26386-3',
  display: 'Ankle - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRay2Views: Coding = {
  code: '26387-1',
  display: 'Ankle - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralXRay2Views: Coding = {
  code: '26388-9',
  display: 'Hand - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRay2Views: Coding = {
  code: '26389-7',
  display: 'Hand - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRay2Views: Coding = {
  code: '26390-5',
  display: 'Hand - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusBilateralXRay2Views: Coding = {
  code: '26391-3',
  display: 'Humerus - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusLeftXRay2Views: Coding = {
  code: '26392-1',
  display: 'Humerus - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusRightXRay2Views: Coding = {
  code: '26393-9',
  display: 'Humerus - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRay2Views: Coding = {
  code: '26394-7',
  display: 'Knee - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay2Views: Coding = {
  code: '26395-4',
  display: 'Knee - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay2Views: Coding = {
  code: '26396-2',
  display: 'Knee - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaBilateralXRay2Views: Coding = {
  code: '26397-0',
  display: 'Patella - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaLeftXRay2Views: Coding = {
  code: '26398-8',
  display: 'Patella - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaRightXRay2Views: Coding = {
  code: '26399-6',
  display: 'Patella - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralXRaySingleView: Coding = {
  code: '26400-2',
  display: 'Hip - bilateral X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRaySingleView: Coding = {
  code: '26401-0',
  display: 'Hip - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRaySingleView: Coding = {
  code: '26402-8',
  display: 'Hip - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL18HydroxydeoxycorticosteroneMassTimeIn24HourUrine: Coding = {
  code: '26988-6',
  display: '18-Hydroxydeoxycorticosterone [Mass/time] in 24 hour Urine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRay: Coding = {
  code: '28561-9',
  display: 'Pelvis X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRay: Coding = {
  code: '28564-3',
  display: 'Skull X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRay: Coding = {
  code: '28565-0',
  display: 'Knee X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCT: Coding = {
  code: '28566-8',
  display: 'Spine CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusXRay: Coding = {
  code: '28567-6',
  display: 'Humerus X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianEmergencyDepartmentNote: Coding = {
  code: '28568-4',
  display: 'Physician Emergency department Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianConsultingProgressNote: Coding = {
  code: '28569-2',
  display: 'Physician consulting Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProviderUnspecifiedProcedureNote: Coding = {
  code: '28570-0',
  display: 'Provider-unspecified Procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechLanguagePathologyNote: Coding = {
  code: '28571-8',
  display: 'Speech-language pathology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DentistInitialAssessmentNote: Coding = {
  code: '28572-6',
  display: 'Dentist Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianOperationNote: Coding = {
  code: '28573-4',
  display: 'Physician, Operation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DischargeNote: Coding = {
  code: '28574-2',
  display: 'Discharge note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NursePractitionerProgressNote: Coding = {
  code: '28575-9',
  display: 'Nurse practitioner Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JointMRI: Coding = {
  code: '28576-7',
  display: 'Joint MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DentistProcedureNote: Coding = {
  code: '28577-5',
  display: 'Dentist procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalTherapyNote: Coding = {
  code: '28578-3',
  display: 'Occupational therapy Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicalTherapyNote: Coding = {
  code: '28579-1',
  display: 'Physical therapy Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChiropracticMedicineProgressNote: Coding = {
  code: '28580-9',
  display: 'Chiropractic medicine Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChiropracticMedicineInitialAssessmentNote: Coding = {
  code: '28581-7',
  display: 'Chiropractic medicine Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRay: Coding = {
  code: '28582-5',
  display: 'Hand X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DentistOperationNote: Coding = {
  code: '28583-3',
  display: 'Dentist Operation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRay: Coding = {
  code: '28613-8',
  display: 'Spine X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverUS: Coding = {
  code: '28614-6',
  display: 'Liver US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AudiologyStudy: Coding = {
  code: '28615-3',
  display: 'Audiology study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianTransferNote: Coding = {
  code: '28616-1',
  display: 'Physician Transfer note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DentistryProgressNote: Coding = {
  code: '28617-9',
  display: 'Dentistry Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DentistryNote: Coding = {
  code: '28618-7',
  display: 'Dentistry Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NursePractitionerInitialAssessmentNote: Coding = {
  code: '28621-1',
  display: 'Nurse practitioner Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseDischargeAssessment: Coding = {
  code: '28622-9',
  display: 'Nurse Discharge assessment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseProgressNote: Coding = {
  code: '28623-7',
  display: 'Nurse Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PodiatryOperationNote: Coding = {
  code: '28624-5',
  display: 'Podiatry Operation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PodiatryProcedureNote: Coding = {
  code: '28625-2',
  display: 'Podiatry procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianHistoryAndPhysicalNote: Coding = {
  code: '28626-0',
  display: 'Physician History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychiatryProgressNote: Coding = {
  code: '28627-8',
  display: 'Psychiatry Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychiatryNote: Coding = {
  code: '28628-6',
  display: 'Psychiatry Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PerimetryStudy: Coding = {
  code: '28629-4',
  display: 'Perimetry study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TonometryStudy: Coding = {
  code: '28630-2',
  display: 'Tonometry study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VisualAcuityStudy: Coding = {
  code: '28631-0',
  display: 'Visual acuity study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeterophoriaStudy: Coding = {
  code: '28632-8',
  display: 'Heterophoria study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PolysomnographySleepStudy: Coding = {
  code: '28633-6',
  display: 'Polysomnography (sleep) study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychiatryInitialAssessmentNote: Coding = {
  code: '28635-1',
  display: 'Psychiatry Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProviderUnspecifiedInitialAssessment: Coding = {
  code: '28636-9',
  display: 'Provider-unspecified Initial assessment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseTransferNote: Coding = {
  code: '28651-8',
  display: 'Nurse Transfer note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SocialWorkNote: Coding = {
  code: '28653-4',
  display: 'Social work Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianAttendingInitialAssessmentNote: Coding = {
  code: '28654-2',
  display: 'Physician attending Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianAttendingDischargeSummary: Coding = {
  code: '28655-9',
  display: 'Physician attending Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SocialWorkProgressNote: Coding = {
  code: '28656-7',
  display: 'Social work Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhotoDocumentationEyeRight: Coding = {
  code: '29111-2',
  display: 'Photo documentation Eye - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhotoDocumentationEyeLeft: Coding = {
  code: '29112-0',
  display: 'Photo documentation Eye - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechTherapyServiceAttachment: Coding = {
  code: '29206-0',
  display: 'Speech therapy service attachment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestCTWOContrast: Coding = {
  code: '29252-4',
  display: 'Chest CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EyeUltrasoundStudy: Coding = {
  code: '29272-2',
  display: 'Eye ultrasound study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DialysisRecords: Coding = {
  code: '29749-9',
  display: 'Dialysis records',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeonatalIntensiveCareRecords: Coding = {
  code: '29750-7',
  display: 'Neonatal intensive care records',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CriticalCareRecords: Coding = {
  code: '29751-5',
  display: 'Critical care records',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PerioperativeRecords: Coding = {
  code: '29752-3',
  display: 'Perioperative records',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseInitialAssessmentNote: Coding = {
  code: '29753-1',
  display: 'Nurse Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NystagmogramStudy: Coding = {
  code: '29754-9',
  display: 'Nystagmogram study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NerveConductionStudy: Coding = {
  code: '29755-6',
  display: 'Nerve conduction study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeritoneoscopyStudy: Coding = {
  code: '29756-4',
  display: 'Peritoneoscopy study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColposcopyStudy: Coding = {
  code: '29757-2',
  display: 'Colposcopy study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DentistDischargeSummary: Coding = {
  code: '29761-4',
  display: 'Dentist Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAbscessDrainageOfUnspecifiedBodyRegion: Coding = {
  code: '30578-9',
  display: 'CT Guidance for abscess drainage of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForInjectionOfSpineFacetJoint: Coding = {
  code: '30579-7',
  display: 'CT Guidance for injection of Spine facet joint',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfUnspecifiedBodyRegion: Coding = {
  code: '30580-5',
  display: 'CT Guidance for fine needle aspiration of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForRadiationTreatmentOfUnspecifiedBodyRegionWContrastIV: Coding = {
  code: '30581-3',
  display: 'CT Guidance for radiation treatment of Unspecified body region-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForRadiationTreatmentOfUnspecifiedBodyRegionWOContrast: Coding = {
  code: '30582-1',
  display: 'CT Guidance for radiation treatment of Unspecified body region-- WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalCTWContrastIV: Coding = {
  code: '30583-9',
  display: 'Internal auditory canal CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalCTWOContrast: Coding = {
  code: '30584-7',
  display: 'Internal auditory canal CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasopharynxAndNeckCTWOContrast: Coding = {
  code: '30585-4',
  display: 'Nasopharynx and Neck CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckCTWAndWOContrastIV: Coding = {
  code: '30586-2',
  display: 'Neck CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitBilateralCTWOContrast: Coding = {
  code: '30587-0',
  display: 'Orbit - bilateral CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesCT: Coding = {
  code: '30588-8',
  display: 'Sinuses CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PetrousBoneCTWOContrast: Coding = {
  code: '30589-6',
  display: 'Petrous bone CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PituitaryAndSellaTurcicaCTWContrastIV: Coding = {
  code: '30590-4',
  display: 'Pituitary and Sella turcica CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PituitaryAndSellaTurcicaCTWOContrast: Coding = {
  code: '30591-2',
  display: 'Pituitary and Sella turcica CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalCTWOContrast: Coding = {
  code: '30592-0',
  display: 'Spine Cervical CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVesselsCTAngiogramWAndWOContrastIV: Coding = {
  code: '30593-8',
  display: 'Head vessels CT angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckVesselsCTAngiogramWAndWOContrastIV: Coding = {
  code: '30594-6',
  display: 'Neck vessels CT angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfLung: Coding = {
  code: '30595-3',
  display: 'CT Guidance for fine needle aspiration of Lung',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicCTWContrastIT: Coding = {
  code: '30596-1',
  display: 'Spine Thoracic CT W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicCTWOContrast: Coding = {
  code: '30597-9',
  display: 'Spine Thoracic CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestCTWAndWOContrastIV: Coding = {
  code: '30598-7',
  display: 'Chest CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenCTWContrast: Coding = {
  code: '30599-5',
  display: 'Abdomen CT W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SmallBowelCTViewsEnteroclysisWContrastPOViaDuodenalIntubation: Coding = {
  code: '30600-1',
  display: 'Small bowel CT Views Enteroclysis W contrast PO via duodenal intubation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfAbdomen: Coding = {
  code: '30601-9',
  display: 'CT Guidance for biopsy of Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfAbdomen: Coding = {
  code: '30602-7',
  display: 'CT Guidance for fine needle aspiration of Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfLiver: Coding = {
  code: '30603-5',
  display: 'CT Guidance for fine needle aspiration of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfPancreas: Coding = {
  code: '30604-3',
  display: 'CT Guidance for biopsy of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfPancreas: Coding = {
  code: '30605-0',
  display: 'CT Guidance for fine needle aspiration of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfPelvis: Coding = {
  code: '30606-8',
  display: 'CT Guidance for fine needle aspiration of Pelvis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfKidneyBilateral: Coding = {
  code: '30607-6',
  display: 'CT Guidance for biopsy of Kidney - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfKidneyBilateral: Coding = {
  code: '30608-4',
  display: 'CT Guidance for fine needle aspiration of Kidney - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfSpleen: Coding = {
  code: '30609-2',
  display: 'CT Guidance for biopsy of Spleen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfSpleen: Coding = {
  code: '30610-0',
  display: 'CT Guidance for fine needle aspiration of Spleen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverCTWOContrast: Coding = {
  code: '30611-8',
  display: 'Liver CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverCTWAndWOContrastIV: Coding = {
  code: '30612-6',
  display: 'Liver CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PancreasCTWOContrast: Coding = {
  code: '30613-4',
  display: 'Pancreas CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PancreasCTWAndWOContrastIV: Coding = {
  code: '30614-2',
  display: 'Pancreas CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisCTWOContrast: Coding = {
  code: '30615-9',
  display: 'Pelvis CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisCTWAndWOContrastIV: Coding = {
  code: '30616-7',
  display: 'Pelvis CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointCT: Coding = {
  code: '30619-1',
  display: 'Sacroiliac Joint CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarCTWOContrast: Coding = {
  code: '30620-9',
  display: 'Spine Lumbar CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpleenCTWOContrast: Coding = {
  code: '30621-7',
  display: 'Spleen CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpleenCTWContrastIV: Coding = {
  code: '30622-5',
  display: 'Spleen CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisVesselsCTAngiogramWAndWOContrastIV: Coding = {
  code: '30623-3',
  display: 'Pelvis vessels CT angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityCTWContrastIV: Coding = {
  code: '30624-1',
  display: 'Lower extremity CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityCTWOContrast: Coding = {
  code: '30625-8',
  display: 'Lower extremity CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityCTWContrastIV: Coding = {
  code: '30626-6',
  display: 'Upper extremity CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityCTWOContrast: Coding = {
  code: '30627-4',
  display: 'Upper extremity CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForRemovalOfForeignBodyFromUnspecifiedBodyRegion: Coding = {
  code: '30628-2',
  display: 'Fluoroscopy Guidance for removal of foreign body from Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForProcedureOfUnspecifiedBodyRegion: Coding = {
  code: '30629-0',
  display: 'Fluoroscopy Guidance for procedure of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCisternFluoroscopyVideoWContrast: Coding = {
  code: '30630-8',
  display: 'Head Cistern Fluoroscopy video W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestFluoroscopy: Coding = {
  code: '30631-6',
  display: 'Chest Fluoroscopy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DiaphragmFluoroscopyMotion: Coding = {
  code: '30632-4',
  display: 'Diaphragm Fluoroscopy Motion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EsophagusFluoroscopyWBariumContrastPO: Coding = {
  code: '30633-2',
  display: 'Esophagus Fluoroscopy W barium contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfLung: Coding = {
  code: '30634-0',
  display: 'Fluoroscopy Guidance for biopsy of Lung',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineUpperFluoroscopyAndAPWContrastPO: Coding = {
  code: '30635-7',
  display: 'Gastrointestine upper Fluoroscopy and AP W contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColonFluoroscopyReductionWViewsWContrastPR: Coding = {
  code: '30636-5',
  display: 'Colon Fluoroscopy Reduction W views W contrast PR',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPlacementOfTubeInGastrointestine: Coding = {
  code: '30637-3',
  display: 'Fluoroscopy Guidance for placement of tube in Gastrointestine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfHip: Coding = {
  code: '30638-1',
  display: 'Fluoroscopy Guidance for injection of Hip',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VesselFluoroscopicAngiogramWContrast: Coding = {
  code: '30639-9',
  display: 'Vessel Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinFluoroscopicAngiogramAngioplastyWContrastIV: Coding = {
  code: '30640-7',
  display: 'Vein Fluoroscopic angiogram Angioplasty W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinFluoroscopicAngiogramAdditionalAngioplastyWContrastIV: Coding = {
  code: '30641-5',
  display: 'Vein Fluoroscopic angiogram Additional angioplasty W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopySingleView: Coding = {
  code: '30642-3',
  display: 'Unspecified body region Fluoroscopy Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForPlacementOfCatheterInCentralVein: Coding = {
  code: '30643-1',
  display: 'US Guidance for placement of catheter in Central vein',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForPlacementOfCatheterInCentralVeinTunneled: Coding = {
  code: '30644-9',
  display: 'US Guidance for placement of catheter in Central vein-- Tunneled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SuperiorVenaCavaFluoroscopicAngiogramWContrastIV: Coding = {
  code: '30645-6',
  display: 'Superior vena cava Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForChangeOfTubeInSinusTractWContrast: Coding = {
  code: '30646-4',
  display: 'Fluoroscopy Guidance for change of tube in Sinus tract-- W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BiliaryDuctsAndGallbladderFluoroscopyWContrastViaTTube: Coding = {
  code: '30647-2',
  display: 'Biliary ducts and Gallbladder Fluoroscopy W contrast via T-tube',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeripheralArteryFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '30648-0',
  display: 'Peripheral artery Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeripheralArteryFluoroscopicAngiogramAdditionalAngioplastyWContrastIA: Coding = {
  code: '30649-8',
  display: 'Peripheral artery Fluoroscopic angiogram Additional angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyForShunt: Coding = {
  code: '30650-6',
  display: 'Unspecified body region Fluoroscopy for shunt',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForCoreNeedleBiopsyOfBreast: Coding = {
  code: '30651-4',
  display: 'US Guidance for core needle biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForFineNeedleBiopsyOfBreast: Coding = {
  code: '30652-2',
  display: 'US Guidance for fine needle biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfCystOfBreast: Coding = {
  code: '30653-0',
  display: 'US Guidance for aspiration of cyst of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletMRIWOContrast: Coding = {
  code: '30654-8',
  display: 'Thoracic outlet MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCisternMRI: Coding = {
  code: '30655-5',
  display: 'Head Cistern MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceStereotacticForLocalizationInBrainWContrastIV: Coding = {
  code: '30656-3',
  display: 'MRI Guidance.stereotactic for localization in Brain-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainMRIWOContrast: Coding = {
  code: '30657-1',
  display: 'Brain MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalMRIWOContrast: Coding = {
  code: '30658-9',
  display: 'Internal auditory canal MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalMRIWAndWOContrastIV: Coding = {
  code: '30659-7',
  display: 'Internal auditory canal MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckMRIWOContrast: Coding = {
  code: '30660-5',
  display: 'Neck MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitBilateralMRIWOContrast: Coding = {
  code: '30661-3',
  display: 'Orbit - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesMRIWOContrast: Coding = {
  code: '30662-1',
  display: 'Sinuses MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesMRIWAndWOContrastIV: Coding = {
  code: '30663-9',
  display: 'Sinuses MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceForRadiationTreatmentOfUnspecifiedBodyRegionWContrastIV: Coding = {
  code: '30664-7',
  display: 'MRI Guidance for radiation treatment of Unspecified body region-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceForRadiationTreatmentOfUnspecifiedBodyRegionWOContrast: Coding = {
  code: '30665-4',
  display: 'MRI Guidance for radiation treatment of Unspecified body region-- WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PituitaryAndSellaTurcicaMRIWOContrast: Coding = {
  code: '30666-2',
  display: 'Pituitary and Sella turcica MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalMRIWOContrast: Coding = {
  code: '30667-0',
  display: 'Spine Cervical MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenMRIWOContrast: Coding = {
  code: '30668-8',
  display: 'Abdomen MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverMRIWOContrast: Coding = {
  code: '30669-6',
  display: 'Liver MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverMRIWAndWOContrastIV: Coding = {
  code: '30670-4',
  display: 'Liver MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipMRIWOContrast: Coding = {
  code: '30671-2',
  display: 'Pelvis and Hip MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipMRIWAndWOContrastIV: Coding = {
  code: '30672-0',
  display: 'Pelvis and Hip MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisMRIWOContrast: Coding = {
  code: '30673-8',
  display: 'Pelvis MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisMRIWAndWOContrastIV: Coding = {
  code: '30674-6',
  display: 'Pelvis MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProstateMRI: Coding = {
  code: '30675-3',
  display: 'Prostate MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarMRIWContrastIV: Coding = {
  code: '30678-7',
  display: 'Spine Lumbar MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarMRIWOContrast: Coding = {
  code: '30679-5',
  display: 'Spine Lumbar MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleMRIWOContrast: Coding = {
  code: '30680-3',
  display: 'Ankle MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootMRIWOContrast: Coding = {
  code: '30681-1',
  display: 'Foot MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootMRIWAndWOContrastIV: Coding = {
  code: '30682-9',
  display: 'Foot MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmMRIWOContrast: Coding = {
  code: '30683-7',
  display: 'Forearm MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmMRIWAndWOContrastIV: Coding = {
  code: '30684-5',
  display: 'Forearm MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandMRIWOContrast: Coding = {
  code: '30685-2',
  display: 'Hand MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandMRIWAndWOContrastIV: Coding = {
  code: '30686-0',
  display: 'Hand MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipMRIWOContrast: Coding = {
  code: '30687-8',
  display: 'Hip MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipMRIWAndWOContrastIV: Coding = {
  code: '30688-6',
  display: 'Hip MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmMRIWOContrast: Coding = {
  code: '30689-4',
  display: 'Upper arm MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmMRIWAndWOContrastIV: Coding = {
  code: '30690-2',
  display: 'Upper arm MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeMRIWOContrast: Coding = {
  code: '30691-0',
  display: 'Knee MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityMRI: Coding = {
  code: '30692-8',
  display: 'Lower extremity MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderMRIWOContrast: Coding = {
  code: '30693-6',
  display: 'Shoulder MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanAndUptakeSingle: Coding = {
  code: '30694-4',
  display: 'Thyroid Scan and uptake.single',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScan: Coding = {
  code: '30695-1',
  display: 'Thyroid Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleScan: Coding = {
  code: '30696-9',
  display: 'Scrotum and Testicle Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonarySystemScanVentilationAndPerfusionWRadionuclideInhaledAndWRadionuclideIV: Coding = {
  code: '30697-7',
  display: 'Pulmonary system Scan ventilation and perfusion W radionuclide inhaled and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfCystOfUnspecifiedBodyRegion: Coding = {
  code: '30698-5',
  display: 'US Guidance for aspiration of cyst of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfUnspecifiedBodyRegion: Coding = {
  code: '30699-3',
  display: 'US Guidance for drainage of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfUnspecifiedBodyRegion: Coding = {
  code: '30700-9',
  display: 'US Guidance for needle biopsy of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionUSDuringSurgery: Coding = {
  code: '30701-7',
  display: 'Unspecified body region US during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForInjectionOfThyroid: Coding = {
  code: '30702-5',
  display: 'US Guidance for injection of Thyroid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfPericardialSpace: Coding = {
  code: '30703-3',
  display: 'US Guidance for aspiration of Pericardial space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenUSLimited: Coding = {
  code: '30704-1',
  display: 'Abdomen US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UterusAndFallopianTubesUS: Coding = {
  code: '30705-8',
  display: 'Uterus and Fallopian tubes US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverUSDuringSurgery: Coding = {
  code: '30706-6',
  display: 'Liver US during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityUS: Coding = {
  code: '30709-0',
  display: 'Lower extremity US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityUS: Coding = {
  code: '30710-8',
  display: 'Upper extremity US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipUSDevelopmentalJointAssessment: Coding = {
  code: '30711-6',
  display: 'Hip US developmental joint assessment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipUSWODevelopmentalJointAssessment: Coding = {
  code: '30712-4',
  display: 'Hip US WO developmental joint assessment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRayWRightBendingAndWLeftBending: Coding = {
  code: '30713-2',
  display: 'Spine X-ray W right bending and W left bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAP: Coding = {
  code: '30714-0',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis AP',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPAndLateral: Coding = {
  code: '30715-7',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisLateral: Coding = {
  code: '30716-5',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisStanding: Coding = {
  code: '30717-3',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointXRayTomograph: Coding = {
  code: '30719-9',
  display: 'Temporomandibular joint X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitBilateralXRayForForeignBody: Coding = {
  code: '30720-7',
  display: 'Orbit - bilateral X-ray for foreign body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayPAAndLateral: Coding = {
  code: '30721-5',
  display: 'Sinuses X-ray PA and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRaySingleViewPortable: Coding = {
  code: '30722-3',
  display: 'Skull X-ray Single view portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayPortable: Coding = {
  code: '30723-1',
  display: 'Skull X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRaySingleViewPortable: Coding = {
  code: '30724-9',
  display: 'Spine Cervical X-ray Single view portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPSingleView: Coding = {
  code: '30725-6',
  display: 'Spine Cervical X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPAndLateralPortable: Coding = {
  code: '30726-4',
  display: 'Spine Cervical X-ray AP and lateral portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPPortableSingleView: Coding = {
  code: '30727-2',
  display: 'Spine Cervical X-ray AP portable single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalOdontoidAndCervicalAxisXRayAPPortableSingleView: Coding = {
  code: '30729-8',
  display: 'Spine Cervical Odontoid and Cervical axis X-ray AP portable single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ZygomaticArchBilateralXRayPortable: Coding = {
  code: '30730-6',
  display: 'Zygomatic arch - bilateral X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ZygomaticArchXRayPortable: Coding = {
  code: '30731-4',
  display: 'Zygomatic arch X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayRightAndLeftObliquePortable: Coding = {
  code: '30733-0',
  display: 'Chest X-ray right and left oblique portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPLateralDecubitus: Coding = {
  code: '30734-8',
  display: 'Chest X-ray AP lateral-decubitus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPLateralDecubitusPortable: Coding = {
  code: '30735-5',
  display: 'Chest X-ray AP lateral-decubitus portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayWInspirationAndExpiration: Coding = {
  code: '30736-3',
  display: 'Chest X-ray W inspiration and expiration',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayLeftLateral: Coding = {
  code: '30737-1',
  display: 'Chest X-ray left lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayLeftLateralPortable: Coding = {
  code: '30738-9',
  display: 'Chest X-ray left lateral portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayRightOrLeftObliquePortable: Coding = {
  code: '30739-7',
  display: 'Chest X-ray right or-left oblique portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayRightOrLeftOblique: Coding = {
  code: '30740-5',
  display: 'Chest X-ray right or-left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateralAndLordoticUpright: Coding = {
  code: '30741-3',
  display: 'Chest X-ray PA and lateral and lordotic upright',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateralAndRightObliqueAndLeftOblique: Coding = {
  code: '30742-1',
  display: 'Chest X-ray PA and lateral and right oblique and left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateralAndRightObliqueAndLeftObliquePortable: Coding = {
  code: '30743-9',
  display: 'Chest X-ray PA and lateral and right oblique and left oblique portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateralAndRightOrLeftOblique: Coding = {
  code: '30744-7',
  display: 'Chest X-ray PA and lateral and right or-left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRay: Coding = {
  code: '30745-4',
  display: 'Chest X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPortable: Coding = {
  code: '30746-2',
  display: 'Chest X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsXRayPortable: Coding = {
  code: '30747-0',
  display: 'Ribs X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRaySingleView: Coding = {
  code: '30748-8',
  display: 'Shoulder X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRaySingleViewPortable: Coding = {
  code: '30749-6',
  display: 'Shoulder X-ray Single view portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRay5Views: Coding = {
  code: '30750-4',
  display: 'Shoulder X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayWestPoint: Coding = {
  code: '30751-2',
  display: 'Shoulder X-ray West Point',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayAPSingleView: Coding = {
  code: '30752-0',
  display: 'Spine Thoracic X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayAPAndLateral: Coding = {
  code: '30753-8',
  display: 'Spine Thoracic X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayAPAndLateralPortable: Coding = {
  code: '30754-6',
  display: 'Spine Thoracic X-ray AP and lateral portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayAPPortableSingleView: Coding = {
  code: '30755-3',
  display: 'Spine Thoracic X-ray AP portable single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayLateral: Coding = {
  code: '30756-1',
  display: 'Spine Thoracic X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayLateralPortable: Coding = {
  code: '30757-9',
  display: 'Spine Thoracic X-ray lateral portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayObliqueSingleView: Coding = {
  code: '30758-7',
  display: 'Spine Thoracic X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayObliquePortable: Coding = {
  code: '30759-5',
  display: 'Spine Thoracic X-ray oblique portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralXRayTomograph3ViewsWContrastIV: Coding = {
  code: '30760-3',
  display: 'Kidney - bilateral X-ray tomograph 3 views W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralFluoroscopyWContrastRetrogradeViaUrethra: Coding = {
  code: '30761-1',
  display: 'Kidney - bilateral Fluoroscopy W contrast retrograde via urethra',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayTomograph: Coding = {
  code: '30762-9',
  display: 'Abdomen X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPAndLateralCrosstablePortable: Coding = {
  code: '30763-7',
  display: 'Abdomen X-ray AP and lateral crosstable portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcetabulumBilateralXRayPortable: Coding = {
  code: '30764-5',
  display: 'Acetabulum - bilateral X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcetabulumXRayPortable: Coding = {
  code: '30765-2',
  display: 'Acetabulum X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRay3Views: Coding = {
  code: '30766-0',
  display: 'Pelvis X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipXRay: Coding = {
  code: '30767-8',
  display: 'Pelvis and Hip X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipBilateralXRay: Coding = {
  code: '30768-6',
  display: 'Pelvis and Hip - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipBilateralXRayMaxAbduction: Coding = {
  code: '30769-4',
  display: 'Pelvis and Hip - bilateral X-ray max abduction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipXRayAPAndLateralFrog: Coding = {
  code: '30770-2',
  display: 'Pelvis and Hip X-ray AP and lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayInletAndOutlet: Coding = {
  code: '30771-0',
  display: 'Pelvis X-ray inlet and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayPortable: Coding = {
  code: '30772-8',
  display: 'Pelvis X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRaySingleView: Coding = {
  code: '30773-6',
  display: 'Spine Lumbar X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRaySingleViewPortable: Coding = {
  code: '30774-4',
  display: 'Spine Lumbar X-ray Single view portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRay3Views: Coding = {
  code: '30775-1',
  display: 'Spine Lumbar X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRay3ViewsPortable: Coding = {
  code: '30776-9',
  display: 'Spine Lumbar X-ray 3 views portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPSingleView: Coding = {
  code: '30777-7',
  display: 'Spine Lumbar X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayObliqueSingleView: Coding = {
  code: '30778-5',
  display: 'Spine Lumbar X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRayAPAndLateral: Coding = {
  code: '30779-3',
  display: 'Ankle X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerSecondXRay: Coding = {
  code: '30780-1',
  display: 'Finger second X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerThirdXRay: Coding = {
  code: '30781-9',
  display: 'Finger third X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFourthXRay: Coding = {
  code: '30782-7',
  display: 'Finger fourth X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFifthXRay: Coding = {
  code: '30783-5',
  display: 'Finger fifth X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRay2Views: Coding = {
  code: '30784-3',
  display: 'Foot X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayWForcedDorsiflexion: Coding = {
  code: '30785-0',
  display: 'Foot X-ray W forced dorsiflexion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayLateralFrog: Coding = {
  code: '30786-8',
  display: 'Hip X-ray lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JointXRaySingleView: Coding = {
  code: '30787-6',
  display: 'Joint X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRay3Views: Coding = {
  code: '30788-4',
  display: 'Knee X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRay4Views: Coding = {
  code: '30789-2',
  display: 'Knee X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayTunnel: Coding = {
  code: '30790-0',
  display: 'Knee X-ray tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaXRay: Coding = {
  code: '30791-8',
  display: 'Patella X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaXRayPortable: Coding = {
  code: '30792-6',
  display: 'Patella X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRayAPAndLateral: Coding = {
  code: '30793-4',
  display: 'Wrist X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMRI: Coding = {
  code: '30794-2',
  display: 'Breast MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMRI: Coding = {
  code: '30795-9',
  display: 'Breast - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowMRIWOContrast: Coding = {
  code: '30796-7',
  display: 'Elbow MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRay5Views: Coding = {
  code: '30797-5',
  display: 'Spine Lumbar X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCTWOContrast: Coding = {
  code: '30799-1',
  display: 'Head CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceStereotacticForLocalizationInBrainWOContrast: Coding = {
  code: '30800-7',
  display: 'MRI Guidance.stereotactic for localization in Brain-- WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesAndMaxillaCTWContrastIV: Coding = {
  code: '30801-5',
  display: 'Facial bones and Maxilla CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesAndMaxillaCTWOContrast: Coding = {
  code: '30802-3',
  display: 'Facial bones and Maxilla CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesAndMaxillaCTWAndWOContrastIV: Coding = {
  code: '30803-1',
  display: 'Facial bones and Maxilla CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestVesselsCTAngiogramWAndWOContrastIV: Coding = {
  code: '30804-9',
  display: 'Chest vessels CT angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalVesselsCTAngiogramWAndWOContrastIV: Coding = {
  code: '30805-6',
  display: 'Abdominal vessels CT angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAndFemoralArteryBilateralCTAngiogramWAndWOContrastIV: Coding = {
  code: '30806-4',
  display: 'Aorta and Femoral artery - bilateral CT angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsCTAngiogramWAndWOContrastIV: Coding = {
  code: '30807-2',
  display: 'Lower extremity vessels CT angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndThoracicAndLumbarFluoroscopyWContrastIT: Coding = {
  code: '30808-0',
  display: 'Spine Cervical and Thoracic and Lumbar Fluoroscopy W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperGastrointestineAndSmallBowelFluoroscopyWContrastPO: Coding = {
  code: '30809-8',
  display: 'Upper Gastrointestine and Small bowel Fluoroscopy W contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LacrimalDuctFluoroscopyWContrastIntraLacrimalDuct: Coding = {
  code: '30810-6',
  display: 'Lacrimal duct Fluoroscopy W contrast intra lacrimal duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PosteriorFossaFluoroscopyWContrastIT: Coding = {
  code: '30811-4',
  display: 'Posterior fossa Fluoroscopy W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineCervicalFacetJoint: Coding = {
  code: '30812-2',
  display: 'Fluoroscopy Guidance for injection of Spine Cervical Facet Joint',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungBilateralXRayWContrastIntrabronchial: Coding = {
  code: '30813-0',
  display: 'Lung - bilateral X-ray W contrast intrabronchial',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineThoracicFacetJoint: Coding = {
  code: '30814-8',
  display: 'Fluoroscopy Guidance for injection of Spine Thoracic Facet Joint',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuctWContrastRetrograde: Coding = {
  code: '30815-5',
  display: 'Fluoroscopy Guidance for endoscopy of Biliary ducts and Pancreatic duct-- W contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeritoneumFluoroscopicAngiogramWContrastPercutaneous: Coding = {
  code: '30816-3',
  display: 'Peritoneum Fluoroscopic angiogram W contrast percutaneous',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineLumbarFacetJoint: Coding = {
  code: '30817-1',
  display: 'Fluoroscopy Guidance for injection of Spine Lumbar Facet Joint',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForCatheterizationOfFallopianTubesTranscervical: Coding = {
  code: '30818-9',
  display: 'Fluoroscopy Guidance for catheterization of Fallopian tubes-- transcervical',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EpiduralVeinsFluoroscopicAngiogramWContrastIV: Coding = {
  code: '30819-7',
  display: 'Epidural veins Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryExternalBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30820-5',
  display: 'Carotid artery.external - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryExternalFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30821-3',
  display: 'Carotid artery.external Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadArteryBilateralAndNeckArteryBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30822-1',
  display: 'Head artery - bilateral and Neck artery - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadArteryAndNeckArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30823-9',
  display: 'Head artery and Neck artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IntercranialVesselAndNeckVesselFluoroscopicAngiogramWContrast: Coding = {
  code: '30824-7',
  display: 'Intercranial vessel and Neck Vessel Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitVeinsFluoroscopicAngiogramWContrastIV: Coding = {
  code: '30825-4',
  display: 'Orbit veins Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SagittalSinusAndJugularVeinsFluoroscopicAngiogramWContrastIV: Coding = {
  code: '30826-2',
  display: 'Sagittal sinus and Jugular veins Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SagittalSinusVeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '30827-0',
  display: 'Sagittal sinus vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrachialArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30828-8',
  display: 'Brachial artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalMammaryArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30829-6',
  display: 'Internal mammary artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryArteryBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30830-4',
  display: 'Pulmonary artery - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalArteryBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30831-2',
  display: 'Adrenal artery - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30832-0',
  display: 'Adrenal artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisArteriesFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30833-8',
  display: 'Pelvis arteries Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalArteryBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30834-6',
  display: 'Renal artery - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VisceralArteryFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '30836-1',
  display: 'Visceral artery Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAbdominalFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30837-9',
  display: 'Aorta abdominal Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAndFemoralArteryBilateralFluoroscopicAngiogramRunoffWContrastIA: Coding = {
  code: '30838-7',
  display: 'Aorta and Femoral artery - bilateral Fluoroscopic angiogram runoff W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LymphaticsAbdominalFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '30839-5',
  display: 'Lymphatics abdominal Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LymphaticsAbdominalBilateralFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '30840-3',
  display: 'Lymphatics abdominal - bilateral Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PortalVeinFluoroscopicAngiogramWContrastTranshepatic: Coding = {
  code: '30841-1',
  display: 'Portal vein Fluoroscopic angiogram W contrast transhepatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PortalVeinFluoroscopicAngiogramWContrastTranshepaticAndWHemodynamics: Coding = {
  code: '30842-9',
  display: 'Portal vein Fluoroscopic angiogram W contrast transhepatic and W hemodynamics',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalVeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '30843-7',
  display: 'Adrenal vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalVeinBilateralFluoroscopicAngiogramWContrastIV: Coding = {
  code: '30844-5',
  display: 'Adrenal vein - bilateral Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InferiorVenaCavaFluoroscopicAngiogramWContrastIV: Coding = {
  code: '30845-2',
  display: 'Inferior vena cava Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVeinBilateralFluoroscopicAngiogramWContrastIV: Coding = {
  code: '30846-0',
  display: 'Renal vein - bilateral Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '30847-8',
  display: 'Renal vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteriesFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30848-6',
  display: 'Extremity arteries Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteriesBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30849-4',
  display: 'Extremity arteries - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityLymphaticsFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '30850-2',
  display: 'Extremity lymphatics Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityLymphaticsBilateralFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '30851-0',
  display: 'Extremity lymphatics - bilateral Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeripheralVeinsBilateralFluoroscopicAngiogramWContrastIV: Coding = {
  code: '30852-8',
  display: 'Peripheral veins - bilateral Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastDuctUSWContrastIntraDuct: Coding = {
  code: '30853-6',
  display: 'Breast duct US W contrast intra duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndThoracicAndLumbarMRIWOContrast: Coding = {
  code: '30854-4',
  display: 'Spine Cervical and Thoracic and Lumbar MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndThoracicAndLumbarMRIWAndWOContrastIV: Coding = {
  code: '30855-1',
  display: 'Spine Cervical and Thoracic and Lumbar MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVesselsMRIAngiogram: Coding = {
  code: '30856-9',
  display: 'Head vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NervesCranialMRI: Coding = {
  code: '30857-7',
  display: 'Nerves cranial MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVeinsMRIAngiogram: Coding = {
  code: '30858-5',
  display: 'Head veins MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidVesselsAndNeckVesselsMRIAngiogram: Coding = {
  code: '30859-3',
  display: 'Carotid vessels and Neck Vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasopharynxMRI: Coding = {
  code: '30860-1',
  display: 'Nasopharynx MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndNeckVesselsMRIAngiogram: Coding = {
  code: '30861-9',
  display: 'Aortic arch and Neck vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestVesselsMRIAngiogram: Coding = {
  code: '30862-7',
  display: 'Chest vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalAortaAndArteriesMRIAngiogram: Coding = {
  code: '30863-5',
  display: 'Abdominal Aorta and Arteries MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalVeinsAndIVCMRIAngiogram: Coding = {
  code: '30864-3',
  display: 'Abdominal veins and IVC MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CeliacVesselsAndSuperiorMesentericVesselsMRIAngiogram: Coding = {
  code: '30865-0',
  display: 'Celiac vessels and Superior mesenteric Vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LumbarPlexusMRI: Coding = {
  code: '30866-8',
  display: 'Lumbar plexus MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisVesselsMRIAngiogram: Coding = {
  code: '30867-6',
  display: 'Pelvis vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsMRIAngiogram: Coding = {
  code: '30868-4',
  display: 'Renal vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegMRIWOContrast: Coding = {
  code: '30869-2',
  display: 'Lower leg MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegMRIWAndWOContrastIV: Coding = {
  code: '30870-0',
  display: 'Lower leg MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralVesselsMRIAngiogram: Coding = {
  code: '30871-8',
  display: 'Femoral vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootVesselsMRIAngiogram: Coding = {
  code: '30872-6',
  display: 'Foot vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmVesselsMRIAngiogram: Coding = {
  code: '30873-4',
  display: 'Forearm vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsMRIAngiogram: Coding = {
  code: '30874-2',
  display: 'Lower extremity vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointMRI: Coding = {
  code: '30875-9',
  display: 'Upper extremity .joint MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinsMRIAngiogram: Coding = {
  code: '30876-7',
  display: 'Extremity veins MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralAndRenalVesselsScan: Coding = {
  code: '30877-5',
  display: 'Kidney - bilateral and Renal vessels Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfUnspecifiedBodyRegion: Coding = {
  code: '30878-3',
  display: 'US Guidance for aspiration of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVesselsAndNeckVesselsUSDoppler: Coding = {
  code: '30880-9',
  display: 'Head vessels and Neck vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinUSDoppler: Coding = {
  code: '30881-7',
  display: 'Lower extremity vein US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinUSDoppler: Coding = {
  code: '30882-5',
  display: 'Upper extremity vein US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CoccyxXRay: Coding = {
  code: '30883-3',
  display: 'Coccyx X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumXRay: Coding = {
  code: '30884-1',
  display: 'Sacrum X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisSymphysisPubisXRay: Coding = {
  code: '30885-8',
  display: 'Pelvis symphysis pubis X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsMRIAngiogramWContrastIV: Coding = {
  code: '30887-4',
  display: 'Renal vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibioperonealVesselsMRIAngiogram: Coding = {
  code: '30888-2',
  display: 'Tibioperoneal vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointLeftXRay: Coding = {
  code: '30889-0',
  display: 'Temporomandibular joint - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointRightXRay: Coding = {
  code: '30890-8',
  display: 'Temporomandibular joint - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CervicocerebralArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '30891-6',
  display: 'Cervicocerebral artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForCatheterizationOfBiliaryDuctsAndPancreaticDuctWContrastRetrograde: Coding = {
  code: '30892-4',
  display: 'Fluoroscopy Guidance for catheterization of Biliary ducts and Pancreatic duct-- W contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NonGynecologicalCytologyMethodStudy: Coding = {
  code: '33716-2',
  display: 'Non-gynecological cytology method study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyCervicalOrVaginalSmearOrScrapingStudy: Coding = {
  code: '33717-0',
  display: 'Cytology Cervical or vaginal smear or scraping study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfTissueFineNeedleAspirateCytoStain: Coding = {
  code: '33718-8',
  display: 'Cytology report of Tissue fine needle aspirate Cyto stain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FlowCytometryStudy: Coding = {
  code: '33719-6',
  display: 'Flow cytometry study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BloodBankConsult: Coding = {
  code: '33720-4',
  display: 'Blood bank consult',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneMarrowPathologyBiopsyReport: Coding = {
  code: '33721-2',
  display: 'Bone marrow Pathology biopsy report',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertBoxedWarningSection: Coding = {
  code: '34066-1',
  display: 'FDA package insert Boxed warning section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertIndicationsAndUsageSection: Coding = {
  code: '34067-9',
  display: 'FDA package insert Indications and usage section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertDosageAndAdministrationSection: Coding = {
  code: '34068-7',
  display: 'FDA package insert Dosage and administration section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertHowSuppliedSection: Coding = {
  code: '34069-5',
  display: 'FDA package insert How supplied section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertContraindicationsSection: Coding = {
  code: '34070-3',
  display: 'FDA package insert Contraindications section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertWarningsSection: Coding = {
  code: '34071-1',
  display: 'FDA package insert Warnings section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertGeneralPrecautionsSection: Coding = {
  code: '34072-9',
  display: 'FDA package insert General precautions section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertDrugInteractionsSection: Coding = {
  code: '34073-7',
  display: 'FDA package insert Drug interactions section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertDrugLaboratoryTestInteractionsSection: Coding = {
  code: '34074-5',
  display: 'FDA package insert Drug/laboratory test interactions section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertLaboratoryTestsSection: Coding = {
  code: '34075-2',
  display: 'FDA package insert Laboratory tests section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertInformationForPatientsSection: Coding = {
  code: '34076-0',
  display: 'FDA package insert Information for patients section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertTeratogenicEffectsSection: Coding = {
  code: '34077-8',
  display: 'FDA package insert Teratogenic effects section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertNonteratogenicEffectsSection: Coding = {
  code: '34078-6',
  display: 'FDA package insert Nonteratogenic effects section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertLaborAndDeliverySection: Coding = {
  code: '34079-4',
  display: 'FDA package insert Labor and delivery section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertNursingMothersSection: Coding = {
  code: '34080-2',
  display: 'FDA package insert Nursing mothers section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPediatricUseSection: Coding = {
  code: '34081-0',
  display: 'FDA package insert Pediatric use section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertGeriatricUseSection: Coding = {
  code: '34082-8',
  display: 'FDA package insert Geriatric use section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertCarcinogenesisAndMutagenesisAndImpairmentOfFertilitySection: Coding = {
  code: '34083-6',
  display: 'FDA package insert Carcinogenesis and mutagenesis and impairment of fertility section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertAdverseReactionsSection: Coding = {
  code: '34084-4',
  display: 'FDA package insert Adverse reactions section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertControlledSubstanceSection: Coding = {
  code: '34085-1',
  display: 'FDA package insert Controlled substance section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertAbuseSection: Coding = {
  code: '34086-9',
  display: 'FDA package insert Abuse section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertDependenceSection: Coding = {
  code: '34087-7',
  display: 'FDA package insert Dependence section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertOverdosageSection: Coding = {
  code: '34088-5',
  display: 'FDA package insert Overdosage section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertDescriptionSection: Coding = {
  code: '34089-3',
  display: 'FDA package insert Description section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertClinicalPharmacologySection: Coding = {
  code: '34090-1',
  display: 'FDA package insert Clinical pharmacology section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertAnimalPharmacologyToxicologySection: Coding = {
  code: '34091-9',
  display: 'FDA package insert Animal pharmacology/toxicology section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertClinicalStudiesSection: Coding = {
  code: '34092-7',
  display: 'FDA package insert Clinical studies section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertReferencesSection: Coding = {
  code: '34093-5',
  display: 'FDA package insert References section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyHospitalAdmissionHistoryAndPhysicalNote: Coding = {
  code: '34094-3',
  display: 'Cardiology Hospital Admission history and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ComprehensiveHistoryAndPhysicalNote: Coding = {
  code: '34095-0',
  display: 'Comprehensive history and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NursingFacilityComprehensiveHistoryAndPhysicalNote: Coding = {
  code: '34096-8',
  display: 'Nursing facility Comprehensive history and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NursingFacilityConferenceNote: Coding = {
  code: '34097-6',
  display: 'Nursing facility Conference note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ConferenceNote: Coding = {
  code: '34098-4',
  display: 'Conference note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyConsultNote: Coding = {
  code: '34099-2',
  display: 'Cardiology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IntensiveCareUnitConsultNote: Coding = {
  code: '34100-8',
  display: 'Intensive care unit Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineOutpatientConsultNote: Coding = {
  code: '34101-6',
  display: 'General medicine Outpatient Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychiatryHospitalConsultNote: Coding = {
  code: '34102-4',
  display: 'Psychiatry Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryConsultNote: Coding = {
  code: '34103-2',
  display: 'Pulmonary Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalConsultNote: Coding = {
  code: '34104-0',
  display: 'Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalDischargeSummary: Coding = {
  code: '34105-7',
  display: 'Hospital Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianHospitalDischargeSummary: Coding = {
  code: '34106-5',
  display: 'Physician Hospital Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatientQuoteSHomeEducationNote: Coding = {
  code: '34107-3',
  display: "Patient's home Education note",
  system: 'http://loinc.org',
};
const C80DocTypecodes_OutpatientNote: Coding = {
  code: '34108-1',
  display: 'Outpatient Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_Note: Coding = {
  code: '34109-9',
  display: 'Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DiabetologyOutpatientNote: Coding = {
  code: '34110-7',
  display: 'Diabetology Outpatient Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EmergencyDepartmentNote: Coding = {
  code: '34111-5',
  display: 'Emergency department Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalNote: Coding = {
  code: '34112-3',
  display: 'Hospital Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NursingFacilityNote: Coding = {
  code: '34113-1',
  display: 'Nursing facility Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalGroupCounselingNote: Coding = {
  code: '34114-9',
  display: 'Hospital Group counseling note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MedicalStudentHospitalHistoryAndPhysicalNote: Coding = {
  code: '34115-6',
  display: 'Medical student Hospital History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianNursingFacilityHistoryAndPhysicalNote: Coding = {
  code: '34116-4',
  display: 'Physician Nursing facility History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HistoryAndPhysicalNote: Coding = {
  code: '34117-2',
  display: 'History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatientQuoteSHomeInitialAssessmentNote: Coding = {
  code: '34118-0',
  display: "Patient's home Initial assessment note",
  system: 'http://loinc.org',
};
const C80DocTypecodes_NursingFacilityInitialAssessmentNote: Coding = {
  code: '34119-8',
  display: 'Nursing facility Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OutpatientInitialAssessmentNote: Coding = {
  code: '34120-6',
  display: 'Outpatient Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterventionalProcedureNote: Coding = {
  code: '34121-4',
  display: 'Interventional procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PathologyProcedureNote: Coding = {
  code: '34122-2',
  display: 'Pathology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnesthesiologyHospitalPreoperativeEvaluationAndManagementNote: Coding = {
  code: '34123-0',
  display: 'Anesthesiology Hospital Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyOutpatientProgressNote: Coding = {
  code: '34124-8',
  display: 'Cardiology Outpatient Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CaseManagerPatientQuoteSHomeProgressNote: Coding = {
  code: '34125-5',
  display: "Case manager Patient's home Progress note",
  system: 'http://loinc.org',
};
const C80DocTypecodes_IntensiveCareUnitProgressNote: Coding = {
  code: '34126-3',
  display: 'Intensive care unit Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DentistryHygienistOutpatientProgressNote: Coding = {
  code: '34127-1',
  display: 'Dentistry Hygienist Outpatient Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DentistryOutpatientProgressNote: Coding = {
  code: '34128-9',
  display: 'Dentistry Outpatient Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatientQuoteSHomeProgressNote: Coding = {
  code: '34129-7',
  display: "Patient's home Progress note",
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalProgressNote: Coding = {
  code: '34130-5',
  display: 'Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OutpatientProgressNote: Coding = {
  code: '34131-3',
  display: 'Outpatient Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PharmacyOutpatientProgressNote: Coding = {
  code: '34132-1',
  display: 'Pharmacy Outpatient Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SummaryOfEpisodeNote: Coding = {
  code: '34133-9',
  display: 'Summary of episode note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianAttendingOutpatientSupervisoryNote: Coding = {
  code: '34134-7',
  display: 'Physician attending Outpatient Supervisory note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyPhysicianAttendingOutpatientSupervisoryNote: Coding = {
  code: '34135-4',
  display: 'Cardiology Physician attending Outpatient Supervisory note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastroenterologyPhysicianAttendingOutpatientSupervisoryNote: Coding = {
  code: '34136-2',
  display: 'Gastroenterology Physician attending Outpatient Supervisory note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OutpatientSurgicalOperationNote: Coding = {
  code: '34137-0',
  display: 'Outpatient Surgical operation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TargetedHistoryAndPhysicalNote: Coding = {
  code: '34138-8',
  display: 'Targeted history and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseTelephoneEncounterNote: Coding = {
  code: '34139-6',
  display: 'Nurse Telephone encounter Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseAdmissionEvaluationNote: Coding = {
  code: '34744-3',
  display: 'Nurse Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseDischargeSummary: Coding = {
  code: '34745-0',
  display: 'Nurse Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseNote: Coding = {
  code: '34746-8',
  display: 'Nurse Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NursePreoperativeEvaluationAndManagementNote: Coding = {
  code: '34747-6',
  display: 'Nurse Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TelephoneEncounterNote: Coding = {
  code: '34748-4',
  display: 'Telephone encounter Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnesthesiologyOutpatientConsultNote: Coding = {
  code: '34749-2',
  display: 'Anesthesiology Outpatient Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnesthesiologyNote: Coding = {
  code: '34750-0',
  display: 'Anesthesiology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnesthesiologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '34751-8',
  display: 'Anesthesiology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyNote: Coding = {
  code: '34752-6',
  display: 'Cardiology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyOutpatientNote: Coding = {
  code: '34753-4',
  display: 'Cardiology Outpatient Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CriticalCareMedicineNote: Coding = {
  code: '34754-2',
  display: 'Critical Care Medicine Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CriticalCareMedicineTransferSummaryNote: Coding = {
  code: '34755-9',
  display: 'Critical care medicine Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DentistryConsultNote: Coding = {
  code: '34756-7',
  display: 'Dentistry Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DermatologyConsultNote: Coding = {
  code: '34758-3',
  display: 'Dermatology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DermatologyNote: Coding = {
  code: '34759-1',
  display: 'Dermatology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DiabetologyConsultNote: Coding = {
  code: '34760-9',
  display: 'Diabetology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastroenterologyConsultNote: Coding = {
  code: '34761-7',
  display: 'Gastroenterology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastroenterologyNote: Coding = {
  code: '34762-5',
  display: 'Gastroenterology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineAdmissionHistoryAndPhysicalNote: Coding = {
  code: '34763-3',
  display: 'General medicine Admission history and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineConsultNote: Coding = {
  code: '34764-1',
  display: 'General medicine Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineNote: Coding = {
  code: '34765-8',
  display: 'General medicine Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineOutpatientNote: Coding = {
  code: '34766-6',
  display: 'General medicine Outpatient Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineMedicalStudentNote: Coding = {
  code: '34767-4',
  display: 'General medicine Medical student Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineNurseNote: Coding = {
  code: '34768-2',
  display: 'General medicine Nurse Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicinePhysicianAttendingNote: Coding = {
  code: '34769-0',
  display: 'General medicine Physician attending Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineTransferSummaryNote: Coding = {
  code: '34770-8',
  display: 'General medicine Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryPhysicianAttendingNote: Coding = {
  code: '34773-2',
  display: 'Surgery Physician attending Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryHistoryAndPhysicalNote: Coding = {
  code: '34774-0',
  display: 'Surgery History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeriatricMedicineConsultNote: Coding = {
  code: '34776-5',
  display: 'Geriatric medicine Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ObstetricsAndGynecologyConsultNote: Coding = {
  code: '34777-3',
  display: 'Obstetrics and Gynecology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ObstetricsAndGynecologyNote: Coding = {
  code: '34778-1',
  display: 'Obstetrics and Gynecology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HematologyPlusMedicalOncologyConsultNote: Coding = {
  code: '34779-9',
  display: 'Hematology+Medical Oncology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HematologyPlusMedicalOncologyNote: Coding = {
  code: '34780-7',
  display: 'Hematology+Medical Oncology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InfectiousDiseaseConsultNote: Coding = {
  code: '34781-5',
  display: 'Infectious disease Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InfectiousDiseaseNote: Coding = {
  code: '34782-3',
  display: 'Infectious disease Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KinesiotherapyConsultNote: Coding = {
  code: '34783-1',
  display: 'Kinesiotherapy Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KinesiotherapyNote: Coding = {
  code: '34784-9',
  display: 'Kinesiotherapy Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MentalHealthConsultNote: Coding = {
  code: '34785-6',
  display: 'Mental health Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MentalHealthNote: Coding = {
  code: '34786-4',
  display: 'Mental health Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MentalHealthGroupCounselingNote: Coding = {
  code: '34787-2',
  display: 'Mental health Group counseling note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychiatryConsultNote: Coding = {
  code: '34788-0',
  display: 'Psychiatry Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychiatryGroupCounselingNote: Coding = {
  code: '34790-6',
  display: 'Psychiatry Group counseling note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychologyConsultNote: Coding = {
  code: '34791-4',
  display: 'Psychology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychologyNote: Coding = {
  code: '34792-2',
  display: 'Psychology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychologyGroupCounselingNote: Coding = {
  code: '34793-0',
  display: 'Psychology Group counseling note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterdisciplinaryNote: Coding = {
  code: '34794-8',
  display: 'Interdisciplinary Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NephrologyConsultNote: Coding = {
  code: '34795-5',
  display: 'Nephrology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NephrologyNote: Coding = {
  code: '34796-3',
  display: 'Nephrology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyConsultNote: Coding = {
  code: '34797-1',
  display: 'Neurology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologicalSurgeryConsultNote: Coding = {
  code: '34798-9',
  display: 'Neurological surgery Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologicalSurgeryNote: Coding = {
  code: '34799-7',
  display: 'Neurological surgery Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NutritionAndDieteticsConsultNote: Coding = {
  code: '34800-3',
  display: 'Nutrition and dietetics Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NutritionAndDieteticsNote: Coding = {
  code: '34801-1',
  display: 'Nutrition and dietetics Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalMedicineNote: Coding = {
  code: '34802-9',
  display: 'Occupational medicine Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalMedicineConsultNote: Coding = {
  code: '34803-7',
  display: 'Occupational medicine Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OncologyConsultNote: Coding = {
  code: '34805-2',
  display: 'Oncology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OncologyNote: Coding = {
  code: '34806-0',
  display: 'Oncology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OphthalmologyConsultNote: Coding = {
  code: '34807-8',
  display: 'Ophthalmology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OphthalmologyNote: Coding = {
  code: '34808-6',
  display: 'Ophthalmology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OphthalmologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '34809-4',
  display: 'Ophthalmology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OptometryConsultNote: Coding = {
  code: '34810-2',
  display: 'Optometry Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OptometryNote: Coding = {
  code: '34811-0',
  display: 'Optometry Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OralAndMaxillofacialSurgeryConsultNote: Coding = {
  code: '34812-8',
  display: 'Oral and Maxillofacial Surgery Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OralAndMaxillofacialSurgeryNote: Coding = {
  code: '34813-6',
  display: 'Oral and Maxillofacial Surgery Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrthopaedicSurgeryConsultNote: Coding = {
  code: '34814-4',
  display: 'Orthopaedic surgery Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrthopaedicSurgeryNote: Coding = {
  code: '34815-1',
  display: 'Orthopaedic surgery Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OtolaryngologyConsultNote: Coding = {
  code: '34816-9',
  display: 'Otolaryngology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OtolaryngologyNote: Coding = {
  code: '34817-7',
  display: 'Otolaryngology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OtolaryngologySurgicalOperationNote: Coding = {
  code: '34818-5',
  display: 'Otolaryngology Surgical operation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PathologyEvaluationAndManagementNote: Coding = {
  code: '34819-3',
  display: 'Pathology Evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PharmacyConsultNote: Coding = {
  code: '34820-1',
  display: 'Pharmacy Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PharmacyNote: Coding = {
  code: '34821-9',
  display: 'Pharmacy Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicalMedicineAndRehabilitationConsultNote: Coding = {
  code: '34822-7',
  display: 'Physical medicine and rehabilitation Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicalMedicineAndRehabilitationNote: Coding = {
  code: '34823-5',
  display: 'Physical medicine and rehabilitation Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicalTherapyConsultNote: Coding = {
  code: '34824-3',
  display: 'Physical therapy Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlasticSurgeryConsultNote: Coding = {
  code: '34826-8',
  display: 'Plastic surgery Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlasticSurgeryNote: Coding = {
  code: '34827-6',
  display: 'Plastic surgery Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PodiatryConsultNote: Coding = {
  code: '34828-4',
  display: 'Podiatry Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PodiatryNote: Coding = {
  code: '34829-2',
  display: 'Podiatry Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryNote: Coding = {
  code: '34830-0',
  display: 'Pulmonary Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiationOncologyConsultNote: Coding = {
  code: '34831-8',
  display: 'Radiation oncology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiationOncologyNote: Coding = {
  code: '34832-6',
  display: 'Radiation oncology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RecreationalTherapyConsultNote: Coding = {
  code: '34833-4',
  display: 'Recreational therapy Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RecreationalTherapyNote: Coding = {
  code: '34834-2',
  display: 'Recreational therapy Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RespiratoryTherapyConsultNote: Coding = {
  code: '34837-5',
  display: 'Respiratory therapy Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RespiratoryTherapyNote: Coding = {
  code: '34838-3',
  display: 'Respiratory therapy Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RheumatologyConsultNote: Coding = {
  code: '34839-1',
  display: 'Rheumatology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RheumatologyNote: Coding = {
  code: '34840-9',
  display: 'Rheumatology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SocialWorkConsultNote: Coding = {
  code: '34841-7',
  display: 'Social work Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SocialWorkGroupCounselingNote: Coding = {
  code: '34843-3',
  display: 'Social work Group counseling note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SocialWorkTelephoneEncounterNote: Coding = {
  code: '34844-1',
  display: 'Social work Telephone encounter Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechLanguagePathologyPlusAudiologyConsultNote: Coding = {
  code: '34845-8',
  display: 'Speech-language pathology+Audiology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechLanguagePathologyPlusAudiologyNote: Coding = {
  code: '34846-6',
  display: 'Speech-language pathology+Audiology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryConsultNote: Coding = {
  code: '34847-4',
  display: 'Surgery Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryNote: Coding = {
  code: '34848-2',
  display: 'Surgery Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSurgeryConsultNote: Coding = {
  code: '34849-0',
  display: 'Thoracic surgery Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSurgeryOutpatientNote: Coding = {
  code: '34850-8',
  display: 'Thoracic surgery Outpatient Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrologyConsultNote: Coding = {
  code: '34851-6',
  display: 'Urology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrologyNote: Coding = {
  code: '34852-4',
  display: 'Urology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VascularSurgeryConsultNote: Coding = {
  code: '34853-2',
  display: 'Vascular surgery Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VascularSurgeryOutpatientNote: Coding = {
  code: '34854-0',
  display: 'Vascular surgery Outpatient Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalTherapyConsultNote: Coding = {
  code: '34855-7',
  display: 'Occupational therapy Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EvaluationAndManagementOfAnticoagulationNote: Coding = {
  code: '34856-5',
  display: 'Evaluation and management of anticoagulation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EvaluationAndManagementOfSubstanceAbuseNote: Coding = {
  code: '34857-3',
  display: 'Evaluation and management of substance abuse note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PainMedicineNote: Coding = {
  code: '34858-1',
  display: 'Pain medicine Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EvaluationAndManagementOfHyperlipidemia: Coding = {
  code: '34859-9',
  display: 'Evaluation and management of hyperlipidemia',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EvaluationAndManagementOfHypertension: Coding = {
  code: '34860-7',
  display: 'Evaluation and management of hypertension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DiabetologyNote: Coding = {
  code: '34861-5',
  display: 'Diabetology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicinePhysicianAttendingHospitalAdmissionEvaluationNote: Coding = {
  code: '34862-3',
  display: 'General medicine Physician attending Hospital Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MentalHealthCounselingNote: Coding = {
  code: '34864-9',
  display: 'Mental health Counseling note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychiatryCounselingNote: Coding = {
  code: '34865-6',
  display: 'Psychiatry Counseling note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychologyCounselingNote: Coding = {
  code: '34866-4',
  display: 'Psychology Counseling note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OphthalmologyOutpatientPostoperativeEvaluationAndManagementNote: Coding = {
  code: '34867-2',
  display: 'Ophthalmology Outpatient Postoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrthopaedicSurgerySurgicalOperationNote: Coding = {
  code: '34868-0',
  display: 'Orthopaedic surgery Surgical operation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PharmacyCounselingNote: Coding = {
  code: '34869-8',
  display: 'Pharmacy Counseling note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlasticSurgerySurgicalOperationNote: Coding = {
  code: '34870-6',
  display: 'Plastic surgery Surgical operation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SocialWorkCounselingNote: Coding = {
  code: '34872-2',
  display: 'Social work Counseling note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryAdmissionEvaluationNote: Coding = {
  code: '34873-0',
  display: 'Surgery Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgerySurgicalOperationNote: Coding = {
  code: '34874-8',
  display: 'Surgery Surgical operation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryPostoperativeEvaluationAndManagementNote: Coding = {
  code: '34875-5',
  display: 'Surgery Postoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryPreoperativeEvaluationAndManagementNote: Coding = {
  code: '34876-3',
  display: 'Surgery Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrologySurgicalOperationNote: Coding = {
  code: '34877-1',
  display: 'Urology Surgical operation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EmergencyMedicineNote: Coding = {
  code: '34878-9',
  display: 'Emergency medicine Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EndocrinologyConsultNote: Coding = {
  code: '34879-7',
  display: 'Endocrinology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryNursePostoperativeEvaluationAndManagementNote: Coding = {
  code: '34880-5',
  display: 'Surgery Nurse Postoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryNursePreoperativeEvaluationAndManagementNote: Coding = {
  code: '34881-3',
  display: 'Surgery Nurse Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EducationNote: Coding = {
  code: '34895-3',
  display: 'Education note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyInterventionalProcedureNote: Coding = {
  code: '34896-1',
  display: 'Cardiology Interventional procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DiabetologyEducationNote: Coding = {
  code: '34897-9',
  display: 'Diabetology Education note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EndocrinologyNote: Coding = {
  code: '34898-7',
  display: 'Endocrinology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastroenterologyInterventionalProcedureNote: Coding = {
  code: '34899-5',
  display: 'Gastroenterology Interventional procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineProgressNote: Coding = {
  code: '34900-1',
  display: 'General medicine Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineOutpatientProgressNote: Coding = {
  code: '34901-9',
  display: 'General medicine Outpatient Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeriatricMedicineOutpatientEducationNote: Coding = {
  code: '34902-7',
  display: 'Geriatric medicine Outpatient Education note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MentalHealthProgressNote: Coding = {
  code: '34904-3',
  display: 'Mental health Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyNote: Coding = {
  code: '34905-0',
  display: 'Neurology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PastoralCareNote: Coding = {
  code: '34906-8',
  display: 'Pastoral care Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolGeneralInformationSection: Coding = {
  code: '35510-7',
  display: 'Clinical trial protocol General information section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolBackgroundInformationSection: Coding = {
  code: '35511-5',
  display: 'Clinical trial protocol Background information section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolTrialObjectivesAndPurposeSection: Coding = {
  code: '35512-3',
  display: 'Clinical trial protocol Trial objectives and purpose section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolTrialDesignSection: Coding = {
  code: '35513-1',
  display: 'Clinical trial protocol Trial design section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolSubjectSelectionAndWithdrawalSection: Coding = {
  code: '35514-9',
  display: 'Clinical trial protocol Subject selection and withdrawal section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolSubjectParticipationPlusEpochsSection: Coding = {
  code: '35515-6',
  display: 'Clinical trial protocol Subject participation + epochs section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolTreatmentOfSubjectsPlusEpochsSection: Coding = {
  code: '35516-4',
  display: 'Clinical trial protocol Treatment of subjects + epochs section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolAssessmentSection: Coding = {
  code: '35517-2',
  display: 'Clinical trial protocol Assessment section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolEfficacyAssessmentSection: Coding = {
  code: '35518-0',
  display: 'Clinical trial protocol Efficacy assessment section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolAssessmentOfSafetySection: Coding = {
  code: '35519-8',
  display: 'Clinical trial protocol Assessment of safety section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolStatisticsSection: Coding = {
  code: '35520-6',
  display: 'Clinical trial protocol Statistics section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolDirectAccessToSourceDataPlusDocumentsSection: Coding = {
  code: '35521-4',
  display: 'Clinical trial protocol Direct access to source data+documents section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolQualityControlAndQualityAssuranceSection: Coding = {
  code: '35522-2',
  display: 'Clinical trial protocol Quality control and quality assurance section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolEthicsSection: Coding = {
  code: '35523-0',
  display: 'Clinical trial protocol Ethics section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolDataHandlingAndRecordKeepingSection: Coding = {
  code: '35524-8',
  display: 'Clinical trial protocol Data handling and record keeping section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolFinancingAndInsuranceSection: Coding = {
  code: '35525-5',
  display: 'Clinical trial protocol Financing and insurance section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolPublicationPolicySection: Coding = {
  code: '35526-3',
  display: 'Clinical trial protocol Publication policy section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolSupplementsSection: Coding = {
  code: '35527-1',
  display: 'Clinical trial protocol Supplements section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolClinicalTrialProtocol: Coding = {
  code: '35528-9',
  display: 'Clinical trial protocol Clinical trial protocol',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteryFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '35881-2',
  display: 'Extremity artery Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InferiorVenaCavaFluoroscopicAngiogramAngioplastyWContrastIV: Coding = {
  code: '35882-0',
  display: 'Inferior vena cava Fluoroscopic angiogram Angioplasty W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaFluoroscopicAngiogramAtherectomyWContrastIA: Coding = {
  code: '35883-8',
  display: 'Aorta Fluoroscopic angiogram Atherectomy W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAbscessDrainageOfAbdomen: Coding = {
  code: '35884-6',
  display: 'CT Guidance for abscess drainage of Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForAbscessDrainageOfUnspecifiedBodyRegion: Coding = {
  code: '35885-3',
  display: 'Fluoroscopy Guidance for abscess drainage of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationOfBreast: Coding = {
  code: '35886-1',
  display: 'CT Guidance for aspiration of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationOfCystOfUnspecifiedBodyRegion: Coding = {
  code: '35887-9',
  display: 'CT Guidance for aspiration of cyst of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForAspirationOfHip: Coding = {
  code: '35888-7',
  display: 'Fluoroscopy Guidance for aspiration of Hip',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBronchoscopyOfChest: Coding = {
  code: '35889-5',
  display: 'Fluoroscopy Guidance for bronchoscopy of Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfAbdomen: Coding = {
  code: '35890-3',
  display: 'Fluoroscopy Guidance for biopsy of Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfBone: Coding = {
  code: '35891-1',
  display: 'CT Guidance for biopsy of Bone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfHead: Coding = {
  code: '35892-9',
  display: 'CT Guidance for biopsy of Head',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfBreast: Coding = {
  code: '35893-7',
  display: 'CT Guidance for biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfChest: Coding = {
  code: '35894-5',
  display: 'Fluoroscopy Guidance for biopsy of Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfChest: Coding = {
  code: '35895-2',
  display: 'CT Guidance for biopsy of Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfLowerExtremity: Coding = {
  code: '35896-0',
  display: 'CT Guidance for biopsy of Lower extremity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfUpperExtremity: Coding = {
  code: '35897-8',
  display: 'CT Guidance for biopsy of Upper extremity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfSalivaryGland: Coding = {
  code: '35898-6',
  display: 'CT Guidance for biopsy of Salivary gland',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfKidney: Coding = {
  code: '35899-4',
  display: 'Fluoroscopy Guidance for biopsy of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfLiver: Coding = {
  code: '35900-0',
  display: 'Fluoroscopy Guidance for biopsy of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfLymphNode: Coding = {
  code: '35901-8',
  display: 'CT Guidance for biopsy of Lymph node',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfPancreas: Coding = {
  code: '35902-6',
  display: 'Fluoroscopy Guidance for biopsy of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfProstate: Coding = {
  code: '35903-4',
  display: 'CT Guidance for biopsy of Prostate',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfSpineCervical: Coding = {
  code: '35904-2',
  display: 'CT Guidance for biopsy of Spine Cervical',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfSpineLumbar: Coding = {
  code: '35905-9',
  display: 'CT Guidance for biopsy of Spine Lumbar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfSpineThoracic: Coding = {
  code: '35906-7',
  display: 'CT Guidance for biopsy of Spine Thoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfSpleen: Coding = {
  code: '35907-5',
  display: 'Fluoroscopy Guidance for biopsy of Spleen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfThyroid: Coding = {
  code: '35908-3',
  display: 'CT Guidance for biopsy of Thyroid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfChestWContrastIV: Coding = {
  code: '35909-1',
  display: 'CT Guidance for biopsy of Chest-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfChestWAndWOContrastIV: Coding = {
  code: '35910-9',
  display: 'CT Guidance for biopsy of Chest-- W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfChestWOContrast: Coding = {
  code: '35911-7',
  display: 'CT Guidance for biopsy of Chest-- WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPlacementOfCatheterInUnspecifiedBodyRegion: Coding = {
  code: '35912-5',
  display: 'Fluoroscopy Guidance for placement of catheter in Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfAbdomen: Coding = {
  code: '35913-3',
  display: 'CT Guidance for drainage of Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfAnus: Coding = {
  code: '35914-1',
  display: 'CT Guidance for drainage of Anus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfAppendix: Coding = {
  code: '35915-8',
  display: 'CT Guidance for drainage of Appendix',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfChest: Coding = {
  code: '35916-6',
  display: 'CT Guidance for drainage of Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfGallbladder: Coding = {
  code: '35917-4',
  display: 'CT Guidance for drainage of Gallbladder',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfKidney: Coding = {
  code: '35918-2',
  display: 'CT Guidance for drainage of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfLiver: Coding = {
  code: '35919-0',
  display: 'CT Guidance for drainage of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfLymphNode: Coding = {
  code: '35920-8',
  display: 'CT Guidance for drainage of Lymph node',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfPelvis: Coding = {
  code: '35921-6',
  display: 'CT Guidance for drainage of Pelvis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfUnspecifiedBodyRegion: Coding = {
  code: '35922-4',
  display: 'CT Guidance for drainage of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfChestWContrastIV: Coding = {
  code: '35923-2',
  display: 'CT Guidance for drainage of Chest-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfChestWOContrast: Coding = {
  code: '35924-0',
  display: 'CT Guidance for drainage of Chest-- WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfStomach: Coding = {
  code: '35925-7',
  display: 'Fluoroscopy Guidance for endoscopy of Stomach',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForGastrostomyOfStomach: Coding = {
  code: '35926-5',
  display: 'Fluoroscopy Guidance for gastrostomy of Stomach',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSacroiliacJoint: Coding = {
  code: '35927-3',
  display: 'Fluoroscopy Guidance for injection of Sacroiliac Joint',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForLocalizationOfBreastLeft: Coding = {
  code: '35928-1',
  display: 'CT Guidance for localization of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForLocalizationOfBreastRight: Coding = {
  code: '35929-9',
  display: 'CT Guidance for localization of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNerveBlockOfAbdomen: Coding = {
  code: '35930-7',
  display: 'CT Guidance for nerve block of Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNerveBlockOfPelvis: Coding = {
  code: '35931-5',
  display: 'CT Guidance for nerve block of Pelvis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNerveBlockOfSpineLumbar: Coding = {
  code: '35932-3',
  display: 'CT Guidance for nerve block of Spine Lumbar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForPercutaneousVertebroplastyOfSpine: Coding = {
  code: '35933-1',
  display: 'CT Guidance for percutaneous vertebroplasty of Spine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForPercutaneousVertebroplastyOfSpineLumbar: Coding = {
  code: '35934-9',
  display: 'CT Guidance for percutaneous vertebroplasty of Spine Lumbar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForPercutaneousVertebroplastyOfSpineThoracic: Coding = {
  code: '35935-6',
  display: 'CT Guidance for percutaneous vertebroplasty of Spine Thoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousVertebroplastyOfSpine: Coding = {
  code: '35936-4',
  display: 'Fluoroscopy Guidance for percutaneous vertebroplasty of Spine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForPlacementOfRadiationTherapyFieldsInUnspecifiedBodyRegion: Coding = {
  code: '35937-2',
  display: 'CT Guidance for placement of radiation therapy fields in Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForPlacementOfTubeInChest: Coding = {
  code: '35938-0',
  display: 'CT Guidance for placement of tube in Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRayTomograph: Coding = {
  code: '35939-8',
  display: 'Ankle X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleCT: Coding = {
  code: '35940-6',
  display: 'Ankle CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralCT: Coding = {
  code: '35941-4',
  display: 'Ankle - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftCT: Coding = {
  code: '35942-2',
  display: 'Ankle - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayTomograph: Coding = {
  code: '35943-0',
  display: 'Ankle - left X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightCT: Coding = {
  code: '35944-8',
  display: 'Ankle - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaCT: Coding = {
  code: '35945-5',
  display: 'Aorta CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaMRIAngiogram: Coding = {
  code: '35946-3',
  display: 'Aorta MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaMRI: Coding = {
  code: '35947-1',
  display: 'Aorta MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAbdominalCT: Coding = {
  code: '35948-9',
  display: 'Aorta abdominal CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAbdominalMRI: Coding = {
  code: '35949-7',
  display: 'Aorta abdominal MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaThoracicMRI: Coding = {
  code: '35950-5',
  display: 'Aorta thoracic MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchMRIAngiogram: Coding = {
  code: '35951-3',
  display: 'Aortic arch MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AppendixCT: Coding = {
  code: '35952-1',
  display: 'Appendix CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FaceMRI: Coding = {
  code: '35953-9',
  display: 'Face MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMRI: Coding = {
  code: '35954-7',
  display: 'Breast - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMRI: Coding = {
  code: '35955-4',
  display: 'Breast - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalMRI: Coding = {
  code: '35956-2',
  display: 'Internal auditory canal MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalLeftCT: Coding = {
  code: '35957-0',
  display: 'Internal auditory canal - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalCT: Coding = {
  code: '35958-8',
  display: 'Internal auditory canal CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleXRayTomograph: Coding = {
  code: '35959-6',
  display: 'Clavicle X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleCT: Coding = {
  code: '35960-4',
  display: 'Clavicle CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleMRI: Coding = {
  code: '35961-2',
  display: 'Clavicle MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowCT: Coding = {
  code: '35962-0',
  display: 'Elbow CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowXRayTomograph: Coding = {
  code: '35963-8',
  display: 'Elbow X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralXRayTomograph: Coding = {
  code: '35964-6',
  display: 'Elbow - bilateral X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralCT: Coding = {
  code: '35965-3',
  display: 'Elbow - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftCT: Coding = {
  code: '35966-1',
  display: 'Elbow - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRayTomograph: Coding = {
  code: '35967-9',
  display: 'Elbow - left X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightCT: Coding = {
  code: '35968-7',
  display: 'Elbow - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EsophagusCT: Coding = {
  code: '35969-5',
  display: 'Esophagus CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityXRayTomograph: Coding = {
  code: '35970-3',
  display: 'Extremity X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityCT: Coding = {
  code: '35971-1',
  display: 'Lower extremity CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityXRayTomograph: Coding = {
  code: '35972-9',
  display: 'Lower extremity X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityBilateralCT: Coding = {
  code: '35973-7',
  display: 'Lower extremity - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsBilateralMRIAngiogram: Coding = {
  code: '35974-5',
  display: 'Lower extremity vessels - bilateral MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityBilateralMRI: Coding = {
  code: '35975-2',
  display: 'Lower extremity - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftCT: Coding = {
  code: '35976-0',
  display: 'Lower extremity - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftXRayTomograph: Coding = {
  code: '35977-8',
  display: 'Lower extremity - left X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftMRI: Coding = {
  code: '35978-6',
  display: 'Lower extremity - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightCT: Coding = {
  code: '35979-4',
  display: 'Lower extremity - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightMRI: Coding = {
  code: '35980-2',
  display: 'Lower extremity - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityCT: Coding = {
  code: '35981-0',
  display: 'Upper extremity CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityLeftCT: Coding = {
  code: '35982-8',
  display: 'Upper extremity - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityRightCT: Coding = {
  code: '35983-6',
  display: 'Upper extremity - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurCT: Coding = {
  code: '35984-4',
  display: 'Femur CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurXRayTomograph: Coding = {
  code: '35985-1',
  display: 'Femur X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurBilateralXRayTomograph: Coding = {
  code: '35986-9',
  display: 'Femur - bilateral X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurLeftCT: Coding = {
  code: '35987-7',
  display: 'Femur - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurLeftXRayTomograph: Coding = {
  code: '35988-5',
  display: 'Femur - left X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightCT: Coding = {
  code: '35989-3',
  display: 'Femur - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FetalMRI: Coding = {
  code: '35990-1',
  display: 'Fetal MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootCT: Coding = {
  code: '35991-9',
  display: 'Foot CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayTomograph: Coding = {
  code: '35992-7',
  display: 'Foot X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralCT: Coding = {
  code: '35993-5',
  display: 'Foot - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftCT: Coding = {
  code: '35994-3',
  display: 'Foot - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRayTomograph: Coding = {
  code: '35995-0',
  display: 'Foot - left X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightCT: Coding = {
  code: '35996-8',
  display: 'Foot - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmCT: Coding = {
  code: '35997-6',
  display: 'Forearm CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmBilateralCT: Coding = {
  code: '35998-4',
  display: 'Forearm - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmLeftCT: Coding = {
  code: '35999-2',
  display: 'Forearm - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmRightCT: Coding = {
  code: '36000-8',
  display: 'Forearm - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GallbladderXRayTomograph: Coding = {
  code: '36001-6',
  display: 'Gallbladder X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandCT: Coding = {
  code: '36002-4',
  display: 'Hand CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRayTomograph: Coding = {
  code: '36003-2',
  display: 'Hand X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralCT: Coding = {
  code: '36004-0',
  display: 'Hand - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftCT: Coding = {
  code: '36005-7',
  display: 'Hand - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRayTomograph: Coding = {
  code: '36006-5',
  display: 'Hand - left X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightCT: Coding = {
  code: '36007-3',
  display: 'Hand - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristAndHandMRI: Coding = {
  code: '36008-1',
  display: 'Wrist and Hand MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartMRIAngiogram: Coding = {
  code: '36009-9',
  display: 'Heart MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusCT: Coding = {
  code: '36010-7',
  display: 'Calcaneus CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusXRayTomograph: Coding = {
  code: '36011-5',
  display: 'Calcaneus X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayTomograph: Coding = {
  code: '36012-3',
  display: 'Hip X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipMRI: Coding = {
  code: '36013-1',
  display: 'Hip MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipCT: Coding = {
  code: '36014-9',
  display: 'Hip CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralXRayTomograph: Coding = {
  code: '36015-6',
  display: 'Hip - bilateral X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralCT: Coding = {
  code: '36016-4',
  display: 'Hip - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralMRI: Coding = {
  code: '36017-2',
  display: 'Hip - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftCT: Coding = {
  code: '36018-0',
  display: 'Hip - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayTomograph: Coding = {
  code: '36019-8',
  display: 'Hip - left X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftMRI: Coding = {
  code: '36020-6',
  display: 'Hip - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightCT: Coding = {
  code: '36021-4',
  display: 'Hip - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightMRI: Coding = {
  code: '36022-2',
  display: 'Hip - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmCT: Coding = {
  code: '36023-0',
  display: 'Upper arm CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusXRayTomograph: Coding = {
  code: '36024-8',
  display: 'Humerus X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmMRI: Coding = {
  code: '36025-5',
  display: 'Upper arm MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmBilateralCT: Coding = {
  code: '36026-3',
  display: 'Upper arm - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmLeftCT: Coding = {
  code: '36027-1',
  display: 'Upper arm - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmLeftMRI: Coding = {
  code: '36028-9',
  display: 'Upper arm - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmRightCT: Coding = {
  code: '36029-7',
  display: 'Upper arm - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmRightMRI: Coding = {
  code: '36030-5',
  display: 'Upper arm - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointMRI: Coding = {
  code: '36031-3',
  display: 'Sacroiliac Joint MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyXRayTomograph: Coding = {
  code: '36032-1',
  display: 'Kidney X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyMRI: Coding = {
  code: '36033-9',
  display: 'Kidney MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralMRI: Coding = {
  code: '36034-7',
  display: 'Kidney - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyLeftMRI: Coding = {
  code: '36035-4',
  display: 'Kidney - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyRightMRI: Coding = {
  code: '36036-2',
  display: 'Kidney - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeCT: Coding = {
  code: '36037-0',
  display: 'Knee CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayTomograph: Coding = {
  code: '36038-8',
  display: 'Knee X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayTomograph: Coding = {
  code: '36039-6',
  display: 'Knee - bilateral X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralCT: Coding = {
  code: '36040-4',
  display: 'Knee - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftCT: Coding = {
  code: '36041-2',
  display: 'Knee - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayTomograph: Coding = {
  code: '36042-0',
  display: 'Knee - left X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightCT: Coding = {
  code: '36043-8',
  display: 'Knee - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LarynxXRayTomograph: Coding = {
  code: '36044-6',
  display: 'Larynx X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LarynxMRI: Coding = {
  code: '36045-3',
  display: 'Larynx MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverMRI: Coding = {
  code: '36046-1',
  display: 'Liver MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleCT: Coding = {
  code: '36047-9',
  display: 'Mandible CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleXRayTomograph: Coding = {
  code: '36048-7',
  display: 'Mandible X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MaxillaAndMandibleCT: Coding = {
  code: '36049-5',
  display: 'Maxilla and Mandible CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MaxillaCT: Coding = {
  code: '36050-3',
  display: 'Maxilla CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckCT: Coding = {
  code: '36051-1',
  display: 'Neck CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PancreasMRI: Coding = {
  code: '36052-9',
  display: 'Pancreas MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParathyroidMRI: Coding = {
  code: '36053-7',
  display: 'Parathyroid MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletCT: Coding = {
  code: '36054-5',
  display: 'Thoracic outlet CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PosteriorFossaCT: Coding = {
  code: '36055-2',
  display: 'Posterior fossa CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PosteriorFossaMRI: Coding = {
  code: '36056-0',
  display: 'Posterior fossa MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProstateCT: Coding = {
  code: '36057-8',
  display: 'Prostate CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumCT: Coding = {
  code: '36058-6',
  display: 'Sacrum CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumMRI: Coding = {
  code: '36059-4',
  display: 'Sacrum MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumAndCoccyxMRI: Coding = {
  code: '36060-2',
  display: 'Sacrum and Coccyx MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaMRI: Coding = {
  code: '36061-0',
  display: 'Scapula MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderCT: Coding = {
  code: '36062-8',
  display: 'Shoulder CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralCT: Coding = {
  code: '36063-6',
  display: 'Shoulder - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftCT: Coding = {
  code: '36064-4',
  display: 'Shoulder - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayTomograph: Coding = {
  code: '36065-1',
  display: 'Shoulder - left X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightCT: Coding = {
  code: '36066-9',
  display: 'Shoulder - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineMRI: Coding = {
  code: '36067-7',
  display: 'Spine MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayTomograph: Coding = {
  code: '36068-5',
  display: 'Spine Cervical X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayTomograph: Coding = {
  code: '36069-3',
  display: 'Spine Lumbar X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpleenMRI: Coding = {
  code: '36070-1',
  display: 'Spleen MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternumCT: Coding = {
  code: '36071-9',
  display: 'Sternum CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternumMRI: Coding = {
  code: '36072-7',
  display: 'Sternum MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleMRI: Coding = {
  code: '36073-5',
  display: 'Scrotum and Testicle MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegCT: Coding = {
  code: '36074-3',
  display: 'Lower leg CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegLeftMRI: Coding = {
  code: '36075-0',
  display: 'Lower leg - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegRightMRI: Coding = {
  code: '36076-8',
  display: 'Lower leg - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PortalVeinMRIAngiogram: Coding = {
  code: '36077-6',
  display: 'Portal vein MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVeinMRIAngiogram: Coding = {
  code: '36078-4',
  display: 'Renal vein MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinsMRIAngiogram: Coding = {
  code: '36079-2',
  display: 'Lower extremity veins MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinsMRIAngiogram: Coding = {
  code: '36080-0',
  display: 'Upper extremity veins MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VenaCavaMRIAngiogram: Coding = {
  code: '36081-8',
  display: 'Vena cava MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InferiorVenaCavaMRIAngiogram: Coding = {
  code: '36082-6',
  display: 'Inferior vena cava MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InferiorVenaCavaMRI: Coding = {
  code: '36083-4',
  display: 'Inferior vena cava MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsMRIAngiogram: Coding = {
  code: '36084-2',
  display: 'Upper extremity vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckVesselsMRIAngiogram: Coding = {
  code: '36085-9',
  display: 'Neck vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenCTLimited: Coding = {
  code: '36086-7',
  display: 'Abdomen CT limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCTLimited: Coding = {
  code: '36087-5',
  display: 'Head CT limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalMRILimited: Coding = {
  code: '36088-3',
  display: 'Internal auditory canal MRI limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestCTLimited: Coding = {
  code: '36089-1',
  display: 'Chest CT limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityCTLimited: Coding = {
  code: '36090-9',
  display: 'Extremity CT limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartMRILimited: Coding = {
  code: '36091-7',
  display: 'Heart MRI limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipCTLimited: Coding = {
  code: '36092-5',
  display: 'Hip CT limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointMRILimited: Coding = {
  code: '36093-3',
  display: 'Lower Extremity Joint MRI limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointMRILimited: Coding = {
  code: '36094-1',
  display: 'Upper extremity .joint MRI limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenCTLimitedWContrastIV: Coding = {
  code: '36095-8',
  display: 'Abdomen CT limited W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainMRILimitedWContrastIV: Coding = {
  code: '36096-6',
  display: 'Brain MRI limited W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityCTLimitedWContrastIV: Coding = {
  code: '36097-4',
  display: 'Upper extremity CT limited W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisCTLimitedWContrastIV: Coding = {
  code: '36098-2',
  display: 'Pelvis CT limited W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalCTLimitedWContrastIV: Coding = {
  code: '36099-0',
  display: 'Spine Cervical CT limited W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarMRILimitedWContrastIV: Coding = {
  code: '36100-6',
  display: 'Spine Lumbar MRI limited W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicMRILimitedWContrastIV: Coding = {
  code: '36101-4',
  display: 'Spine Thoracic MRI limited W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenCTLimitedWAndWOContrastIV: Coding = {
  code: '36102-2',
  display: 'Abdomen CT limited W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenCTLimitedWOContrast: Coding = {
  code: '36103-0',
  display: 'Abdomen CT limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCTLimitedWOContrast: Coding = {
  code: '36104-8',
  display: 'Head CT limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainMRILimitedWOContrast: Coding = {
  code: '36105-5',
  display: 'Brain MRI limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityCTLimitedWOContrast: Coding = {
  code: '36106-3',
  display: 'Lower extremity CT limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointLeftMRILimitedWOContrast: Coding = {
  code: '36107-1',
  display: 'Lower extremity joint - left MRI limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisCTLimitedWOContrast: Coding = {
  code: '36108-9',
  display: 'Pelvis CT limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalCTLimitedWOContrast: Coding = {
  code: '36109-7',
  display: 'Spine Cervical CT limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarCTLimitedWOContrast: Coding = {
  code: '36110-5',
  display: 'Spine Lumbar CT limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarMRILimitedWOContrast: Coding = {
  code: '36111-3',
  display: 'Spine Lumbar MRI limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicMRILimitedWOContrast: Coding = {
  code: '36112-1',
  display: 'Spine Thoracic MRI limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyMRIWContrastIV: Coding = {
  code: '36113-9',
  display: 'Kidney MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMRIDynamicWContrastIV: Coding = {
  code: '36114-7',
  display: 'Breast - bilateral MRI dynamic W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleMRIWContrastIntraarticular: Coding = {
  code: '36115-4',
  display: 'Ankle MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftMRIWContrastIntraarticular: Coding = {
  code: '36116-2',
  display: 'Ankle - left MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightMRIWContrastIntraarticular: Coding = {
  code: '36117-0',
  display: 'Ankle - right MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftMRIWContrastIntraarticular: Coding = {
  code: '36118-8',
  display: 'Elbow - left MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightMRIWContrastIntraarticular: Coding = {
  code: '36119-6',
  display: 'Elbow - right MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipMRIWContrastIntraarticular: Coding = {
  code: '36120-4',
  display: 'Hip MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftMRIWContrastIntraarticular: Coding = {
  code: '36121-2',
  display: 'Hip - left MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightMRIWContrastIntraarticular: Coding = {
  code: '36122-0',
  display: 'Hip - right MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointCTWContrastIntraarticular: Coding = {
  code: '36123-8',
  display: 'Sacroiliac Joint CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeCTWContrastIntraarticular: Coding = {
  code: '36124-6',
  display: 'Knee CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeMRIWContrastIntraarticular: Coding = {
  code: '36125-3',
  display: 'Knee MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftMRIWContrastIntraarticular: Coding = {
  code: '36126-1',
  display: 'Knee - left MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightMRIWContrastIntraarticular: Coding = {
  code: '36127-9',
  display: 'Knee - right MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderCTWContrastIntraarticular: Coding = {
  code: '36128-7',
  display: 'Shoulder CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderMRIWContrastIntraarticular: Coding = {
  code: '36129-5',
  display: 'Shoulder MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftMRIWContrastIntraarticular: Coding = {
  code: '36130-3',
  display: 'Shoulder - left MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightCTWContrastIntraarticular: Coding = {
  code: '36131-1',
  display: 'Shoulder - right CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightMRIWContrastIntraarticular: Coding = {
  code: '36132-9',
  display: 'Shoulder - right MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenMRIWContrastIV: Coding = {
  code: '36134-5',
  display: 'Abdomen MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleCTWContrastIV: Coding = {
  code: '36135-2',
  display: 'Ankle CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleMRIWContrastIV: Coding = {
  code: '36136-0',
  display: 'Ankle MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftCTWContrastIV: Coding = {
  code: '36137-8',
  display: 'Ankle - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftMRIWContrastIV: Coding = {
  code: '36138-6',
  display: 'Ankle - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightCTWContrastIV: Coding = {
  code: '36139-4',
  display: 'Ankle - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightMRIWContrastIV: Coding = {
  code: '36140-2',
  display: 'Ankle - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaCTAngiogramWContrastIV: Coding = {
  code: '36141-0',
  display: 'Aorta CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaCTWContrastIV: Coding = {
  code: '36142-8',
  display: 'Aorta CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAbdominalCTWContrastIV: Coding = {
  code: '36143-6',
  display: 'Aorta abdominal CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchCTAngiogramWContrastIV: Coding = {
  code: '36144-4',
  display: 'Aortic arch CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AppendixCTWContrastIV: Coding = {
  code: '36145-1',
  display: 'Appendix CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryCTAngiogramWContrastIV: Coding = {
  code: '36146-9',
  display: 'Carotid artery CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryArteryCTAngiogramWContrastIV: Coding = {
  code: '36147-7',
  display: 'Pulmonary artery CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FaceMRIWContrastIV: Coding = {
  code: '36148-5',
  display: 'Face MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMRIWContrastIV: Coding = {
  code: '36149-3',
  display: 'Breast MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMRIWContrastIV: Coding = {
  code: '36150-1',
  display: 'Breast - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMRIWContrastIV: Coding = {
  code: '36151-9',
  display: 'Breast - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMRIWContrastIV: Coding = {
  code: '36152-7',
  display: 'Breast - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusLeftCTWContrastIV: Coding = {
  code: '36153-5',
  display: 'Calcaneus - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusRightCTWContrastIV: Coding = {
  code: '36154-3',
  display: 'Calcaneus - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalMRIWContrastIV: Coding = {
  code: '36155-0',
  display: 'Internal auditory canal MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestMRIWContrastIV: Coding = {
  code: '36156-8',
  display: 'Chest MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowCTWContrastIV: Coding = {
  code: '36157-6',
  display: 'Elbow CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowMRIWContrastIV: Coding = {
  code: '36158-4',
  display: 'Elbow MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftCTWContrastIV: Coding = {
  code: '36159-2',
  display: 'Elbow - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftMRIWContrastIV: Coding = {
  code: '36160-0',
  display: 'Elbow - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightCTWContrastIV: Coding = {
  code: '36161-8',
  display: 'Elbow - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightMRIWContrastIV: Coding = {
  code: '36162-6',
  display: 'Elbow - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityBilateralMRIWContrastIV: Coding = {
  code: '36163-4',
  display: 'Lower extremity - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftCTWContrastIV: Coding = {
  code: '36164-2',
  display: 'Lower extremity - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftMRIWContrastIV: Coding = {
  code: '36165-9',
  display: 'Lower extremity - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightCTWContrastIV: Coding = {
  code: '36166-7',
  display: 'Lower extremity - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightMRIWContrastIV: Coding = {
  code: '36167-5',
  display: 'Lower extremity - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityBilateralCTWContrastIV: Coding = {
  code: '36168-3',
  display: 'Upper extremity - bilateral CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityLeftCTWContrastIV: Coding = {
  code: '36169-1',
  display: 'Upper extremity - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityRightCTWContrastIV: Coding = {
  code: '36170-9',
  display: 'Upper extremity - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityRightMRIWContrastIV: Coding = {
  code: '36171-7',
  display: 'Upper extremity - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurCTWContrastIV: Coding = {
  code: '36172-5',
  display: 'Femur CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighMRIWContrastIV: Coding = {
  code: '36173-3',
  display: 'Thigh MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurLeftCTWContrastIV: Coding = {
  code: '36174-1',
  display: 'Femur - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighLeftMRIWContrastIV: Coding = {
  code: '36175-8',
  display: 'Thigh - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightCTWContrastIV: Coding = {
  code: '36176-6',
  display: 'Femur - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighRightMRIWContrastIV: Coding = {
  code: '36177-4',
  display: 'Thigh - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootCTWContrastIV: Coding = {
  code: '36178-2',
  display: 'Foot CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootMRIWContrastIV: Coding = {
  code: '36179-0',
  display: 'Foot MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralMRIWContrastIV: Coding = {
  code: '36180-8',
  display: 'Foot - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftCTWContrastIV: Coding = {
  code: '36181-6',
  display: 'Foot - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftMRIWContrastIV: Coding = {
  code: '36182-4',
  display: 'Foot - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightCTWContrastIV: Coding = {
  code: '36183-2',
  display: 'Foot - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightMRIWContrastIV: Coding = {
  code: '36184-0',
  display: 'Foot - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmCTWContrastIV: Coding = {
  code: '36185-7',
  display: 'Forearm CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmMRIWContrastIV: Coding = {
  code: '36186-5',
  display: 'Forearm MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmLeftCTWContrastIV: Coding = {
  code: '36187-3',
  display: 'Forearm - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmLeftMRIWContrastIV: Coding = {
  code: '36188-1',
  display: 'Forearm - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmRightCTWContrastIV: Coding = {
  code: '36189-9',
  display: 'Forearm - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmRightMRIWContrastIV: Coding = {
  code: '36190-7',
  display: 'Forearm - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandCTWContrastIV: Coding = {
  code: '36191-5',
  display: 'Hand CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandMRIWContrastIV: Coding = {
  code: '36192-3',
  display: 'Hand MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftCTWContrastIV: Coding = {
  code: '36193-1',
  display: 'Hand - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftMRIWContrastIV: Coding = {
  code: '36194-9',
  display: 'Hand - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightCTWContrastIV: Coding = {
  code: '36195-6',
  display: 'Hand - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightMRIWContrastIV: Coding = {
  code: '36196-4',
  display: 'Hand - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartMRIWContrastIV: Coding = {
  code: '36197-2',
  display: 'Heart MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusCTWContrastIV: Coding = {
  code: '36198-0',
  display: 'Calcaneus CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipMRIWContrastIV: Coding = {
  code: '36199-8',
  display: 'Hip MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipCTWContrastIV: Coding = {
  code: '36200-4',
  display: 'Hip CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralCTWContrastIV: Coding = {
  code: '36201-2',
  display: 'Hip - bilateral CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralMRIWContrastIV: Coding = {
  code: '36202-0',
  display: 'Hip - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftCTWContrastIV: Coding = {
  code: '36203-8',
  display: 'Hip - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftMRIWContrastIV: Coding = {
  code: '36204-6',
  display: 'Hip - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightCTWContrastIV: Coding = {
  code: '36205-3',
  display: 'Hip - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightMRIWContrastIV: Coding = {
  code: '36206-1',
  display: 'Hip - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmCTWContrastIV: Coding = {
  code: '36207-9',
  display: 'Upper arm CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmMRIWContrastIV: Coding = {
  code: '36208-7',
  display: 'Upper arm MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmLeftCTWContrastIV: Coding = {
  code: '36209-5',
  display: 'Upper arm - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmLeftMRIWContrastIV: Coding = {
  code: '36210-3',
  display: 'Upper arm - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmRightCTWContrastIV: Coding = {
  code: '36211-1',
  display: 'Upper arm - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmRightMRIWContrastIV: Coding = {
  code: '36212-9',
  display: 'Upper arm - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointMRIWContrastIV: Coding = {
  code: '36213-7',
  display: 'Lower Extremity Joint MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointLeftMRIWContrastIV: Coding = {
  code: '36214-5',
  display: 'Lower extremity joint - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointRightMRIWContrastIV: Coding = {
  code: '36215-2',
  display: 'Lower extremity joint - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointMRIWContrastIV: Coding = {
  code: '36216-0',
  display: 'Upper extremity .joint MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointCTWContrastIV: Coding = {
  code: '36217-8',
  display: 'Sacroiliac Joint CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointMRIWContrastIV: Coding = {
  code: '36218-6',
  display: 'Sacroiliac Joint MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralMRIWContrastIV: Coding = {
  code: '36219-4',
  display: 'Kidney - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyLeftMRIWContrastIV: Coding = {
  code: '36220-2',
  display: 'Kidney - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyRightMRIWContrastIV: Coding = {
  code: '36221-0',
  display: 'Kidney - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeCTWContrastIV: Coding = {
  code: '36222-8',
  display: 'Knee CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeMRIWContrastIV: Coding = {
  code: '36223-6',
  display: 'Knee MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralMRIWContrastIV: Coding = {
  code: '36224-4',
  display: 'Knee - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftCTWContrastIV: Coding = {
  code: '36225-1',
  display: 'Knee - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftMRIWContrastIV: Coding = {
  code: '36226-9',
  display: 'Knee - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightCTWContrastIV: Coding = {
  code: '36227-7',
  display: 'Knee - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightMRIWContrastIV: Coding = {
  code: '36228-5',
  display: 'Knee - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LarynxCTWContrastIV: Coding = {
  code: '36229-3',
  display: 'Larynx CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LarynxMRIWContrastIV: Coding = {
  code: '36230-1',
  display: 'Larynx MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverMRIWContrastIV: Coding = {
  code: '36231-9',
  display: 'Liver MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleCTWContrastIV: Coding = {
  code: '36232-7',
  display: 'Mandible CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasopharynxMRIWContrastIV: Coding = {
  code: '36233-5',
  display: 'Nasopharynx MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckVesselsCTAngiogramWContrastIV: Coding = {
  code: '36234-3',
  display: 'Neck vessels CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckCTWContrastIV: Coding = {
  code: '36235-0',
  display: 'Neck CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PancreasMRIWContrastIV: Coding = {
  code: '36236-8',
  display: 'Pancreas MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisMRIWContrastIV: Coding = {
  code: '36237-6',
  display: 'Pelvis MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PituitaryAndSellaTurcicaMRIWContrastIV: Coding = {
  code: '36238-4',
  display: 'Pituitary and Sella turcica MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletMRIWContrastIV: Coding = {
  code: '36239-2',
  display: 'Thoracic outlet MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletLeftMRIWContrastIV: Coding = {
  code: '36240-0',
  display: 'Thoracic outlet - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletRightMRIWContrastIV: Coding = {
  code: '36241-8',
  display: 'Thoracic outlet - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PosteriorFossaCTWContrastIV: Coding = {
  code: '36242-6',
  display: 'Posterior fossa CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PosteriorFossaMRIWContrastIV: Coding = {
  code: '36243-4',
  display: 'Posterior fossa MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProstateMRIWContrastIV: Coding = {
  code: '36244-2',
  display: 'Prostate MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumCTWContrastIV: Coding = {
  code: '36245-9',
  display: 'Sacrum CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumMRIWContrastIV: Coding = {
  code: '36246-7',
  display: 'Sacrum MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumAndCoccyxMRIWContrastIV: Coding = {
  code: '36247-5',
  display: 'Sacrum and Coccyx MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaLeftMRIWContrastIV: Coding = {
  code: '36248-3',
  display: 'Scapula - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaRightMRIWContrastIV: Coding = {
  code: '36249-1',
  display: 'Scapula - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderCTWContrastIV: Coding = {
  code: '36250-9',
  display: 'Shoulder CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderMRIWContrastIV: Coding = {
  code: '36251-7',
  display: 'Shoulder MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftCTWContrastIV: Coding = {
  code: '36252-5',
  display: 'Shoulder - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightCTWContrastIV: Coding = {
  code: '36253-3',
  display: 'Shoulder - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightMRIWContrastIV: Coding = {
  code: '36254-1',
  display: 'Shoulder - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesCTWContrastIV: Coding = {
  code: '36255-8',
  display: 'Sinuses CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineMRIWContrastIV: Coding = {
  code: '36256-6',
  display: 'Spine MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternumCTWContrastIV: Coding = {
  code: '36257-4',
  display: 'Sternum CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegCTWContrastIV: Coding = {
  code: '36258-2',
  display: 'Lower leg CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegMRIWContrastIV: Coding = {
  code: '36259-0',
  display: 'Lower leg MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegLeftCTWContrastIV: Coding = {
  code: '36260-8',
  display: 'Lower leg - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegLeftMRIWContrastIV: Coding = {
  code: '36261-6',
  display: 'Lower leg - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegRightCTWContrastIV: Coding = {
  code: '36262-4',
  display: 'Lower leg - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegRightMRIWContrastIV: Coding = {
  code: '36263-2',
  display: 'Lower leg - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UterusCTWContrastIV: Coding = {
  code: '36264-0',
  display: 'Uterus CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UterusMRIWContrastIV: Coding = {
  code: '36265-7',
  display: 'Uterus MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestVesselsCTAngiogramWContrastIV: Coding = {
  code: '36266-5',
  display: 'Chest vessels CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenCTWAndWOContrastIV: Coding = {
  code: '36267-3',
  display: 'Abdomen CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleCTWAndWOContrastIV: Coding = {
  code: '36268-1',
  display: 'Ankle CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftCTWAndWOContrastIV: Coding = {
  code: '36269-9',
  display: 'Ankle - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightCTWAndWOContrastIV: Coding = {
  code: '36270-7',
  display: 'Ankle - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAbdominalCTWAndWOContrastIV: Coding = {
  code: '36271-5',
  display: 'Aorta abdominal CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAbdominalMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36272-3',
  display: 'Aorta abdominal MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAbdominalMRIWAndWOContrastIV: Coding = {
  code: '36273-1',
  display: 'Aorta abdominal MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaThoracicMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36274-9',
  display: 'Aorta thoracic MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalArteryMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36275-6',
  display: 'Renal artery MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMRIWAndWOContrastIV: Coding = {
  code: '36276-4',
  display: 'Breast MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMRIWAndWOContrastIV: Coding = {
  code: '36277-2',
  display: 'Breast - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMRIWAndWOContrastIV: Coding = {
  code: '36278-0',
  display: 'Breast - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMRIWAndWOContrastIV: Coding = {
  code: '36279-8',
  display: 'Breast - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusLeftCTWAndWOContrastIV: Coding = {
  code: '36280-6',
  display: 'Calcaneus - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusRightCTWAndWOContrastIV: Coding = {
  code: '36281-4',
  display: 'Calcaneus - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalCTWAndWOContrastIV: Coding = {
  code: '36282-2',
  display: 'Internal auditory canal CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestMRIWAndWOContrastIV: Coding = {
  code: '36283-0',
  display: 'Chest MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenMRIWAndWOContrastIV: Coding = {
  code: '36284-8',
  display: 'Chest and Abdomen MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowCTWAndWOContrastIV: Coding = {
  code: '36285-5',
  display: 'Elbow CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftCTWAndWOContrastIV: Coding = {
  code: '36286-3',
  display: 'Elbow - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightCTWAndWOContrastIV: Coding = {
  code: '36287-1',
  display: 'Elbow - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityCTWAndWOContrastIV: Coding = {
  code: '36288-9',
  display: 'Lower extremity CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityBilateralMRIWAndWOContrastIV: Coding = {
  code: '36289-7',
  display: 'Lower extremity - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftCTWAndWOContrastIV: Coding = {
  code: '36290-5',
  display: 'Lower extremity - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftMRIWAndWOContrastIV: Coding = {
  code: '36291-3',
  display: 'Lower extremity - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightCTWAndWOContrastIV: Coding = {
  code: '36292-1',
  display: 'Lower extremity - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRay3Views: Coding = {
  code: '36293-9',
  display: 'Abdomen X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRay3Views: Coding = {
  code: '36294-7',
  display: 'Ankle X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRay3Views: Coding = {
  code: '36295-4',
  display: 'Ankle - bilateral X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRay3Views: Coding = {
  code: '36296-2',
  display: 'Ankle - left X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesXRay3Views: Coding = {
  code: '36297-0',
  display: 'Facial bones X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRay3Views: Coding = {
  code: '36298-8',
  display: 'Chest X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowXRay3Views: Coding = {
  code: '36299-6',
  display: 'Elbow X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralXRay3Views: Coding = {
  code: '36300-2',
  display: 'Elbow - bilateral X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRay3Views: Coding = {
  code: '36301-0',
  display: 'Elbow - left X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurXRay3Views: Coding = {
  code: '36302-8',
  display: 'Femur X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerXRay3Views: Coding = {
  code: '36303-6',
  display: 'Finger X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerLeftXRay3Views: Coding = {
  code: '36304-4',
  display: 'Finger - left X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRay3Views: Coding = {
  code: '36305-1',
  display: 'Foot X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralXRay3Views: Coding = {
  code: '36306-9',
  display: 'Foot - bilateral X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRay3Views: Coding = {
  code: '36307-7',
  display: 'Foot - left X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralXRay3Views: Coding = {
  code: '36308-5',
  display: 'Hip - bilateral X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRay3Views: Coding = {
  code: '36309-3',
  display: 'Hip - left X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRay3Views: Coding = {
  code: '36310-1',
  display: 'Knee - bilateral X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay3Views: Coding = {
  code: '36311-9',
  display: 'Knee - left X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleXRay3Views: Coding = {
  code: '36312-7',
  display: 'Mandible X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsBilateralXRay3Views: Coding = {
  code: '36313-5',
  display: 'Ribs - bilateral X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLeftXRay3Views: Coding = {
  code: '36314-3',
  display: 'Ribs - left X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbLeftXRay3Views: Coding = {
  code: '36315-0',
  display: 'Thumb - left X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesLeftXRay3Views: Coding = {
  code: '36316-8',
  display: 'Toes - left X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRay4Views: Coding = {
  code: '36317-6',
  display: 'Ankle X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesXRay4Views: Coding = {
  code: '36318-4',
  display: 'Facial bones X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogram4Views: Coding = {
  code: '36319-2',
  display: 'Breast Mammogram 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRay4Views: Coding = {
  code: '36320-0',
  display: 'Chest X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestFluoroscopy4Views: Coding = {
  code: '36321-8',
  display: 'Chest Fluoroscopy 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralXRay4Views: Coding = {
  code: '36322-6',
  display: 'Elbow - bilateral X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRay4Views: Coding = {
  code: '36323-4',
  display: 'Elbow - left X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurLeftXRay4Views: Coding = {
  code: '36324-2',
  display: 'Femur - left X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRay4Views: Coding = {
  code: '36325-9',
  display: 'Knee - bilateral X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay4Views: Coding = {
  code: '36326-7',
  display: 'Knee - left X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleXRay4Views: Coding = {
  code: '36327-5',
  display: 'Mandible X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsBilateralXRay4Views: Coding = {
  code: '36328-3',
  display: 'Ribs - bilateral X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRay4Views: Coding = {
  code: '36329-1',
  display: 'Shoulder - bilateral X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRay4Views: Coding = {
  code: '36330-9',
  display: 'Shoulder - left X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRay4Views: Coding = {
  code: '36331-7',
  display: 'Spine Cervical X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRay4Views: Coding = {
  code: '36332-5',
  display: 'Spine Lumbar X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightMRIWAndWOContrastIV: Coding = {
  code: '36333-3',
  display: 'Lower extremity - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityCTWAndWOContrastIV: Coding = {
  code: '36334-1',
  display: 'Upper extremity CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityLeftCTWAndWOContrastIV: Coding = {
  code: '36335-8',
  display: 'Upper extremity - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityRightCTWAndWOContrastIV: Coding = {
  code: '36336-6',
  display: 'Upper extremity - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityRightMRIWAndWOContrastIV: Coding = {
  code: '36337-4',
  display: 'Upper extremity - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurCTWAndWOContrastIV: Coding = {
  code: '36338-2',
  display: 'Femur CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurLeftCTWAndWOContrastIV: Coding = {
  code: '36339-0',
  display: 'Femur - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightCTWAndWOContrastIV: Coding = {
  code: '36340-8',
  display: 'Femur - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootCTWAndWOContrastIV: Coding = {
  code: '36341-6',
  display: 'Foot CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralMRIWAndWOContrastIV: Coding = {
  code: '36342-4',
  display: 'Foot - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftCTWAndWOContrastIV: Coding = {
  code: '36343-2',
  display: 'Foot - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftMRIWAndWOContrastIV: Coding = {
  code: '36344-0',
  display: 'Foot - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightCTWAndWOContrastIV: Coding = {
  code: '36345-7',
  display: 'Foot - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightMRIWAndWOContrastIV: Coding = {
  code: '36346-5',
  display: 'Foot - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmCTWAndWOContrastIV: Coding = {
  code: '36347-3',
  display: 'Forearm CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmLeftCTWAndWOContrastIV: Coding = {
  code: '36348-1',
  display: 'Forearm - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmLeftMRIWAndWOContrastIV: Coding = {
  code: '36349-9',
  display: 'Forearm - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmRightCTWAndWOContrastIV: Coding = {
  code: '36350-7',
  display: 'Forearm - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmRightMRIWAndWOContrastIV: Coding = {
  code: '36351-5',
  display: 'Forearm - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandCTWAndWOContrastIV: Coding = {
  code: '36352-3',
  display: 'Hand CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftCTWAndWOContrastIV: Coding = {
  code: '36353-1',
  display: 'Hand - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftMRIWAndWOContrastIV: Coding = {
  code: '36354-9',
  display: 'Hand - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightCTWAndWOContrastIV: Coding = {
  code: '36355-6',
  display: 'Hand - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightMRIWAndWOContrastIV: Coding = {
  code: '36356-4',
  display: 'Hand - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartMRIWAndWOContrastIV: Coding = {
  code: '36357-2',
  display: 'Heart MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusCTWAndWOContrastIV: Coding = {
  code: '36358-0',
  display: 'Calcaneus CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipCTWAndWOContrastIV: Coding = {
  code: '36359-8',
  display: 'Hip CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralCTWAndWOContrastIV: Coding = {
  code: '36360-6',
  display: 'Hip - bilateral CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralMRIWAndWOContrastIV: Coding = {
  code: '36361-4',
  display: 'Hip - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftCTWAndWOContrastIV: Coding = {
  code: '36362-2',
  display: 'Hip - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftMRIWAndWOContrastIV: Coding = {
  code: '36363-0',
  display: 'Hip - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightCTWAndWOContrastIV: Coding = {
  code: '36364-8',
  display: 'Hip - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightMRIWAndWOContrastIV: Coding = {
  code: '36365-5',
  display: 'Hip - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmCTWAndWOContrastIV: Coding = {
  code: '36366-3',
  display: 'Upper arm CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmLeftCTWAndWOContrastIV: Coding = {
  code: '36367-1',
  display: 'Upper arm - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmLeftMRIWAndWOContrastIV: Coding = {
  code: '36368-9',
  display: 'Upper arm - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmRightCTWAndWOContrastIV: Coding = {
  code: '36369-7',
  display: 'Upper arm - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmRightMRIWAndWOContrastIV: Coding = {
  code: '36370-5',
  display: 'Upper arm - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointMRIWAndWOContrastIV: Coding = {
  code: '36371-3',
  display: 'Lower Extremity Joint MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointLeftMRIWAndWOContrastIV: Coding = {
  code: '36372-1',
  display: 'Lower extremity joint - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointRightMRIWAndWOContrastIV: Coding = {
  code: '36373-9',
  display: 'Lower extremity joint - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointMRIWAndWOContrastIV: Coding = {
  code: '36374-7',
  display: 'Upper extremity .joint MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointCTWAndWOContrastIV: Coding = {
  code: '36375-4',
  display: 'Sacroiliac Joint CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointMRIWAndWOContrastIV: Coding = {
  code: '36376-2',
  display: 'Sacroiliac Joint MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralCTWAndWOContrastIV: Coding = {
  code: '36377-0',
  display: 'Kidney - bilateral CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralMRIWAndWOContrastIV: Coding = {
  code: '36378-8',
  display: 'Kidney - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeCTWAndWOContrastIV: Coding = {
  code: '36379-6',
  display: 'Knee CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftCTWAndWOContrastIV: Coding = {
  code: '36380-4',
  display: 'Knee - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightCTWAndWOContrastIV: Coding = {
  code: '36381-2',
  display: 'Knee - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LarynxMRIWAndWOContrastIV: Coding = {
  code: '36382-0',
  display: 'Larynx MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleCTWAndWOContrastIV: Coding = {
  code: '36383-8',
  display: 'Mandible CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasopharynxMRIWAndWOContrastIV: Coding = {
  code: '36384-6',
  display: 'Nasopharynx MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PancreasMRIWAndWOContrastIV: Coding = {
  code: '36385-3',
  display: 'Pancreas MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PosteriorFossaCTWAndWOContrastIV: Coding = {
  code: '36387-9',
  display: 'Posterior fossa CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PosteriorFossaMRIWAndWOContrastIV: Coding = {
  code: '36388-7',
  display: 'Posterior fossa MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProstateMRIWAndWOContrastIV: Coding = {
  code: '36389-5',
  display: 'Prostate MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumCTWAndWOContrastIV: Coding = {
  code: '36390-3',
  display: 'Sacrum CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumMRIWAndWOContrastIV: Coding = {
  code: '36391-1',
  display: 'Sacrum MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumAndCoccyxMRIWAndWOContrastIV: Coding = {
  code: '36392-9',
  display: 'Sacrum and Coccyx MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaLeftMRIWAndWOContrastIV: Coding = {
  code: '36393-7',
  display: 'Scapula - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaRightMRIWAndWOContrastIV: Coding = {
  code: '36394-5',
  display: 'Scapula - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderCTWAndWOContrastIV: Coding = {
  code: '36395-2',
  display: 'Shoulder CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftCTWAndWOContrastIV: Coding = {
  code: '36396-0',
  display: 'Shoulder - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightCTWAndWOContrastIV: Coding = {
  code: '36397-8',
  display: 'Shoulder - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesCTWAndWOContrastIV: Coding = {
  code: '36398-6',
  display: 'Sinuses CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCTWAndWOContrastIV: Coding = {
  code: '36399-4',
  display: 'Spine CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineMRIWAndWOContrastIV: Coding = {
  code: '36400-0',
  display: 'Spine MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalCTWAndWOContrastIV: Coding = {
  code: '36401-8',
  display: 'Spine Cervical CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarCTWAndWOContrastIV: Coding = {
  code: '36402-6',
  display: 'Spine Lumbar CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicCTWAndWOContrastIV: Coding = {
  code: '36403-4',
  display: 'Spine Thoracic CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpleenMRIWAndWOContrastIV: Coding = {
  code: '36404-2',
  display: 'Spleen MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternumCTWAndWOContrastIV: Coding = {
  code: '36405-9',
  display: 'Sternum CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleMRIWAndWOContrastIV: Coding = {
  code: '36406-7',
  display: 'Scrotum and Testicle MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidMRIWAndWOContrastIV: Coding = {
  code: '36407-5',
  display: 'Thyroid MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegCTWAndWOContrastIV: Coding = {
  code: '36408-3',
  display: 'Lower leg CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegLeftCTWAndWOContrastIV: Coding = {
  code: '36409-1',
  display: 'Lower leg - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegLeftMRIWAndWOContrastIV: Coding = {
  code: '36410-9',
  display: 'Lower leg - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegRightCTWAndWOContrastIV: Coding = {
  code: '36411-7',
  display: 'Lower leg - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegRightMRIWAndWOContrastIV: Coding = {
  code: '36412-5',
  display: 'Lower leg - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UterusMRIWAndWOContrastIV: Coding = {
  code: '36413-3',
  display: 'Uterus MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PortalVeinMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36414-1',
  display: 'Portal vein MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVeinMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36415-8',
  display: 'Renal vein MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36416-6',
  display: 'Lower extremity veins MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36417-4',
  display: 'Upper extremity veins MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InferiorVenaCavaMRIWAndWOContrastIV: Coding = {
  code: '36418-2',
  display: 'Inferior vena cava MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SuperiorVenaCavaMRIWAndWOContrastIV: Coding = {
  code: '36419-0',
  display: 'Superior vena cava MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36420-8',
  display: 'Chest vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsCTAngiogramWAndWOContrastIV: Coding = {
  code: '36421-6',
  display: 'Upper extremity vessels CT angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36422-4',
  display: 'Upper extremity vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36423-2',
  display: 'Neck vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenCTWOContrast: Coding = {
  code: '36424-0',
  display: 'Abdomen CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleCTWOContrast: Coding = {
  code: '36425-7',
  display: 'Ankle CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftCTWOContrast: Coding = {
  code: '36426-5',
  display: 'Ankle - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftMRIWOContrast: Coding = {
  code: '36427-3',
  display: 'Ankle - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightCTWOContrast: Coding = {
  code: '36428-1',
  display: 'Ankle - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightMRIWOContrast: Coding = {
  code: '36429-9',
  display: 'Ankle - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaCTWOContrast: Coding = {
  code: '36430-7',
  display: 'Aorta CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAbdominalCTWOContrast: Coding = {
  code: '36431-5',
  display: 'Aorta abdominal CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAbdominalMRIAngiogramWOContrast: Coding = {
  code: '36432-3',
  display: 'Aorta abdominal MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaThoracicMRIAngiogramWOContrast: Coding = {
  code: '36433-1',
  display: 'Aorta thoracic MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AppendixCTWOContrast: Coding = {
  code: '36434-9',
  display: 'Appendix CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FaceMRIWOContrast: Coding = {
  code: '36435-6',
  display: 'Face MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMRIWOContrast: Coding = {
  code: '36436-4',
  display: 'Breast MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMRIWOContrast: Coding = {
  code: '36437-2',
  display: 'Breast - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMRIWOContrast: Coding = {
  code: '36438-0',
  display: 'Breast - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMRIWOContrast: Coding = {
  code: '36439-8',
  display: 'Breast - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusLeftCTWOContrast: Coding = {
  code: '36440-6',
  display: 'Calcaneus - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusRightCTWOContrast: Coding = {
  code: '36441-4',
  display: 'Calcaneus - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestMRIWOContrast: Coding = {
  code: '36442-2',
  display: 'Chest MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowCTWOContrast: Coding = {
  code: '36443-0',
  display: 'Elbow CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralCTWOContrast: Coding = {
  code: '36444-8',
  display: 'Elbow - bilateral CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftCTWOContrast: Coding = {
  code: '36445-5',
  display: 'Elbow - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftMRIWOContrast: Coding = {
  code: '36446-3',
  display: 'Elbow - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightCTWOContrast: Coding = {
  code: '36447-1',
  display: 'Elbow - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightMRIWOContrast: Coding = {
  code: '36448-9',
  display: 'Elbow - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityBilateralCTWOContrast: Coding = {
  code: '36449-7',
  display: 'Lower extremity - bilateral CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsBilateralMRIAngiogramWOContrast: Coding = {
  code: '36450-5',
  display: 'Lower extremity vessels - bilateral MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityBilateralMRIWOContrast: Coding = {
  code: '36451-3',
  display: 'Lower extremity - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftCTWOContrast: Coding = {
  code: '36452-1',
  display: 'Lower extremity - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftMRIWOContrast: Coding = {
  code: '36453-9',
  display: 'Lower extremity - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightCTWOContrast: Coding = {
  code: '36454-7',
  display: 'Lower extremity - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightMRIWOContrast: Coding = {
  code: '36455-4',
  display: 'Lower extremity - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityBilateralCTWOContrast: Coding = {
  code: '36456-2',
  display: 'Upper extremity - bilateral CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityLeftCTWOContrast: Coding = {
  code: '36457-0',
  display: 'Upper extremity - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityRightCTWOContrast: Coding = {
  code: '36458-8',
  display: 'Upper extremity - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityRightMRIWOContrast: Coding = {
  code: '36459-6',
  display: 'Upper extremity - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurCTWOContrast: Coding = {
  code: '36460-4',
  display: 'Femur CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighMRIWOContrast: Coding = {
  code: '36461-2',
  display: 'Thigh MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurLeftCTWOContrast: Coding = {
  code: '36462-0',
  display: 'Femur - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighLeftMRIWOContrast: Coding = {
  code: '36463-8',
  display: 'Thigh - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightCTWOContrast: Coding = {
  code: '36464-6',
  display: 'Femur - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighRightMRIWOContrast: Coding = {
  code: '36465-3',
  display: 'Thigh - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootCTWOContrast: Coding = {
  code: '36466-1',
  display: 'Foot CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralMRIWOContrast: Coding = {
  code: '36467-9',
  display: 'Foot - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftCTWOContrast: Coding = {
  code: '36468-7',
  display: 'Foot - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftMRIWOContrast: Coding = {
  code: '36469-5',
  display: 'Foot - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightCTWOContrast: Coding = {
  code: '36470-3',
  display: 'Foot - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightMRIWOContrast: Coding = {
  code: '36471-1',
  display: 'Foot - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmCTWOContrast: Coding = {
  code: '36472-9',
  display: 'Forearm CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmLeftCTWOContrast: Coding = {
  code: '36473-7',
  display: 'Forearm - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmLeftMRIWOContrast: Coding = {
  code: '36474-5',
  display: 'Forearm - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmRightCTWOContrast: Coding = {
  code: '36475-2',
  display: 'Forearm - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmRightMRIWOContrast: Coding = {
  code: '36476-0',
  display: 'Forearm - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandCTWOContrast: Coding = {
  code: '36477-8',
  display: 'Hand CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftCTWOContrast: Coding = {
  code: '36478-6',
  display: 'Hand - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftMRIWOContrast: Coding = {
  code: '36479-4',
  display: 'Hand - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightCTWOContrast: Coding = {
  code: '36480-2',
  display: 'Hand - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightMRIWOContrast: Coding = {
  code: '36481-0',
  display: 'Hand - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartMRIWOContrast: Coding = {
  code: '36482-8',
  display: 'Heart MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusCTWOContrast: Coding = {
  code: '36483-6',
  display: 'Calcaneus CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipCTWOContrast: Coding = {
  code: '36484-4',
  display: 'Hip CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralCTWOContrast: Coding = {
  code: '36485-1',
  display: 'Hip - bilateral CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralMRIWOContrast: Coding = {
  code: '36486-9',
  display: 'Hip - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftCTWOContrast: Coding = {
  code: '36487-7',
  display: 'Hip - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftMRIWOContrast: Coding = {
  code: '36488-5',
  display: 'Hip - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightCTWOContrast: Coding = {
  code: '36489-3',
  display: 'Hip - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightMRIWOContrast: Coding = {
  code: '36490-1',
  display: 'Hip - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmCTWOContrast: Coding = {
  code: '36491-9',
  display: 'Upper arm CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmLeftCTWOContrast: Coding = {
  code: '36492-7',
  display: 'Upper arm - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmLeftMRIWOContrast: Coding = {
  code: '36493-5',
  display: 'Upper arm - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmRightCTWOContrast: Coding = {
  code: '36494-3',
  display: 'Upper arm - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmRightMRIWOContrast: Coding = {
  code: '36495-0',
  display: 'Upper arm - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointMRIWOContrast: Coding = {
  code: '36496-8',
  display: 'Acromioclavicular Joint MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointMRIWOContrast: Coding = {
  code: '36497-6',
  display: 'Lower Extremity Joint MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointLeftMRIWOContrast: Coding = {
  code: '36498-4',
  display: 'Lower extremity joint - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointRightMRIWOContrast: Coding = {
  code: '36499-2',
  display: 'Lower extremity joint - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointMRIWOContrast: Coding = {
  code: '36500-7',
  display: 'Upper extremity .joint MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointCTWOContrast: Coding = {
  code: '36501-5',
  display: 'Sacroiliac Joint CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointMRIWOContrast: Coding = {
  code: '36502-3',
  display: 'Sacroiliac Joint MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralCTWOContrast: Coding = {
  code: '36503-1',
  display: 'Kidney - bilateral CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralMRIWOContrast: Coding = {
  code: '36504-9',
  display: 'Kidney - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeCTWOContrast: Coding = {
  code: '36505-6',
  display: 'Knee CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralMRIWOContrast: Coding = {
  code: '36506-4',
  display: 'Knee - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftCTWOContrast: Coding = {
  code: '36507-2',
  display: 'Knee - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftMRIWOContrast: Coding = {
  code: '36508-0',
  display: 'Knee - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightCTWOContrast: Coding = {
  code: '36509-8',
  display: 'Knee - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightMRIWOContrast: Coding = {
  code: '36510-6',
  display: 'Knee - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LarynxCTWOContrast: Coding = {
  code: '36511-4',
  display: 'Larynx CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleCTWOContrast: Coding = {
  code: '36512-2',
  display: 'Mandible CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasopharynxMRIWOContrast: Coding = {
  code: '36513-0',
  display: 'Nasopharynx MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckCTWOContrast: Coding = {
  code: '36514-8',
  display: 'Neck CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PancreasMRIWOContrast: Coding = {
  code: '36515-5',
  display: 'Pancreas MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletRightMRIWOContrast: Coding = {
  code: '36516-3',
  display: 'Thoracic outlet - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PosteriorFossaCTWOContrast: Coding = {
  code: '36517-1',
  display: 'Posterior fossa CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PosteriorFossaMRIWOContrast: Coding = {
  code: '36518-9',
  display: 'Posterior fossa MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProstateMRIWOContrast: Coding = {
  code: '36519-7',
  display: 'Prostate MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumCTWOContrast: Coding = {
  code: '36520-5',
  display: 'Sacrum CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumMRIWOContrast: Coding = {
  code: '36521-3',
  display: 'Sacrum MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumAndCoccyxMRIWOContrast: Coding = {
  code: '36522-1',
  display: 'Sacrum and Coccyx MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaLeftMRIWOContrast: Coding = {
  code: '36523-9',
  display: 'Scapula - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderCTWOContrast: Coding = {
  code: '36524-7',
  display: 'Shoulder CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralMRIWOContrast: Coding = {
  code: '36525-4',
  display: 'Shoulder - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftCTWOContrast: Coding = {
  code: '36526-2',
  display: 'Shoulder - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightCTWOContrast: Coding = {
  code: '36527-0',
  display: 'Shoulder - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightMRIWOContrast: Coding = {
  code: '36528-8',
  display: 'Shoulder - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesCTWOContrast: Coding = {
  code: '36529-6',
  display: 'Sinuses CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCTWOContrast: Coding = {
  code: '36530-4',
  display: 'Spine CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineMRIWOContrast: Coding = {
  code: '36531-2',
  display: 'Spine MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicMRIWOContrast: Coding = {
  code: '36532-0',
  display: 'Spine Thoracic MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpleenMRIWOContrast: Coding = {
  code: '36533-8',
  display: 'Spleen MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternumCTWOContrast: Coding = {
  code: '36534-6',
  display: 'Sternum CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleMRIWOContrast: Coding = {
  code: '36535-3',
  display: 'Scrotum and Testicle MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidMRIWOContrast: Coding = {
  code: '36536-1',
  display: 'Thyroid MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegCTWOContrast: Coding = {
  code: '36537-9',
  display: 'Lower leg CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegLeftCTWOContrast: Coding = {
  code: '36538-7',
  display: 'Lower leg - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegLeftMRIWOContrast: Coding = {
  code: '36539-5',
  display: 'Lower leg - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegRightCTWOContrast: Coding = {
  code: '36540-3',
  display: 'Lower leg - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegRightMRIWOContrast: Coding = {
  code: '36541-1',
  display: 'Lower leg - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UterusMRIWOContrast: Coding = {
  code: '36542-9',
  display: 'Uterus MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PortalVeinMRIAngiogramWOContrast: Coding = {
  code: '36543-7',
  display: 'Portal vein MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVeinMRIAngiogramWOContrast: Coding = {
  code: '36544-5',
  display: 'Renal vein MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InferiorVenaCavaMRIWOContrast: Coding = {
  code: '36545-2',
  display: 'Inferior vena cava MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SuperiorVenaCavaMRIWOContrast: Coding = {
  code: '36546-0',
  display: 'Superior vena cava MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestVesselsMRIAngiogramWOContrast: Coding = {
  code: '36547-8',
  display: 'Chest vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsMRIAngiogramWOContrast: Coding = {
  code: '36548-6',
  display: 'Upper extremity vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckVesselsMRIAngiogramWOContrast: Coding = {
  code: '36549-4',
  display: 'Neck vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRaySingleView: Coding = {
  code: '36550-2',
  display: 'Abdomen X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRaySingleView: Coding = {
  code: '36551-0',
  display: 'Ankle X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRaySingleView: Coding = {
  code: '36554-4',
  display: 'Chest X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleXRaySingleView: Coding = {
  code: '36555-1',
  display: 'Clavicle X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowXRaySingleView: Coding = {
  code: '36556-9',
  display: 'Elbow X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityBilateralXRaySingleView: Coding = {
  code: '36557-7',
  display: 'Lower extremity - bilateral X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftXRaySingleView: Coding = {
  code: '36558-5',
  display: 'Lower extremity - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurXRaySingleView: Coding = {
  code: '36559-3',
  display: 'Femur X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurLeftXRaySingleView: Coding = {
  code: '36560-1',
  display: 'Femur - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRaySingleView: Coding = {
  code: '36561-9',
  display: 'Foot X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRaySingleView: Coding = {
  code: '36563-5',
  display: 'Hand X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusXRaySingleView: Coding = {
  code: '36564-3',
  display: 'Calcaneus X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusXRaySingleView: Coding = {
  code: '36565-0',
  display: 'Humerus X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRaySingleView: Coding = {
  code: '36566-8',
  display: 'Knee - bilateral X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRaySingleView: Coding = {
  code: '36567-6',
  display: 'Knee - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRaySingleView: Coding = {
  code: '36568-4',
  display: 'Shoulder - bilateral X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRaySingleView: Coding = {
  code: '36569-2',
  display: 'Shoulder - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRaySingleView: Coding = {
  code: '36570-0',
  display: 'Wrist - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRayAPSingleView: Coding = {
  code: '36571-8',
  display: 'Ankle X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPSingleView: Coding = {
  code: '36572-6',
  display: 'Chest X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleXRayAPSingleView: Coding = {
  code: '36573-4',
  display: 'Clavicle X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityXRayAPSingleView: Coding = {
  code: '36574-2',
  display: 'Lower extremity X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurXRayAPSingleView: Coding = {
  code: '36575-9',
  display: 'Femur X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFifthXRayAPSingleView: Coding = {
  code: '36576-7',
  display: 'Finger fifth X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFourthXRayAPSingleView: Coding = {
  code: '36577-5',
  display: 'Finger fourth X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerThirdXRayAPSingleView: Coding = {
  code: '36578-3',
  display: 'Finger third X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayAPSingleView: Coding = {
  code: '36579-1',
  display: 'Foot X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralXRayAPSingleView: Coding = {
  code: '36580-9',
  display: 'Foot - bilateral X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayAPSingleView: Coding = {
  code: '36581-7',
  display: 'Hip X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayAPSingleView: Coding = {
  code: '36582-5',
  display: 'Hip - left X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointLeftXRayAPSingleView: Coding = {
  code: '36583-3',
  display: 'Acromioclavicular joint - left X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayAPSingleView: Coding = {
  code: '36584-1',
  display: 'Knee X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayAPSingleView: Coding = {
  code: '36585-8',
  display: 'Knee - bilateral X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAPSingleView: Coding = {
  code: '36586-6',
  display: 'Shoulder - bilateral X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPSingleView: Coding = {
  code: '36587-4',
  display: 'Shoulder - left X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPPortableSingleView: Coding = {
  code: '36588-2',
  display: 'Abdomen X-ray AP portable single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPPortableSingleView: Coding = {
  code: '36589-0',
  display: 'Chest X-ray AP portable single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayAPAndLateral: Coding = {
  code: '36590-8',
  display: 'Knee - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayLateral: Coding = {
  code: '36591-6',
  display: 'Abdomen X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRayLateral: Coding = {
  code: '36592-4',
  display: 'Ankle X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurXRayLateral: Coding = {
  code: '36593-2',
  display: 'Femur X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFifthXRayLateral: Coding = {
  code: '36594-0',
  display: 'Finger fifth X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFourthXRayLateral: Coding = {
  code: '36595-7',
  display: 'Finger fourth X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerSecondXRayLateral: Coding = {
  code: '36596-5',
  display: 'Finger second X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerThirdXRayLateral: Coding = {
  code: '36597-3',
  display: 'Finger third X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRayLateral: Coding = {
  code: '36598-1',
  display: 'Foot - left X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRayLateral: Coding = {
  code: '36599-9',
  display: 'Hand X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralXRayLateral: Coding = {
  code: '36600-5',
  display: 'Hand - bilateral X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRayLateral: Coding = {
  code: '36601-3',
  display: 'Hand - left X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayLateral: Coding = {
  code: '36602-1',
  display: 'Hip X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayLateral: Coding = {
  code: '36603-9',
  display: 'Hip - left X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayLateral: Coding = {
  code: '36604-7',
  display: 'Knee X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayLateral: Coding = {
  code: '36605-4',
  display: 'Knee - bilateral X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayLateral: Coding = {
  code: '36606-2',
  display: 'Knee - left X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayObliqueSingleView: Coding = {
  code: '36607-0',
  display: 'Abdomen X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowXRayOblique: Coding = {
  code: '36608-8',
  display: 'Elbow X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurXRayObliqueSingleView: Coding = {
  code: '36609-6',
  display: 'Femur X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFifthXRayObliqueSingleView: Coding = {
  code: '36610-4',
  display: 'Finger fifth X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFourthXRayObliqueSingleView: Coding = {
  code: '36611-2',
  display: 'Finger fourth X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerSecondXRayObliqueSingleView: Coding = {
  code: '36612-0',
  display: 'Finger second X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerThirdXRayObliqueSingleView: Coding = {
  code: '36613-8',
  display: 'Finger third X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayObliqueSingleView: Coding = {
  code: '36614-6',
  display: 'Foot X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRayObliqueSingleView: Coding = {
  code: '36615-3',
  display: 'Foot - left X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRayObliqueSingleView: Coding = {
  code: '36616-1',
  display: 'Hand X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayObliqueSingleView: Coding = {
  code: '36617-9',
  display: 'Hip X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralXRayObliqueSingleView: Coding = {
  code: '36618-7',
  display: 'Hip - bilateral X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayOblique: Coding = {
  code: '36619-5',
  display: 'Knee X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayLeftAnteriorOblique: Coding = {
  code: '36620-3',
  display: 'Chest X-ray left anterior oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRayPA: Coding = {
  code: '36621-1',
  display: 'Hand X-ray PA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralXRayPA: Coding = {
  code: '36622-9',
  display: 'Hand - bilateral X-ray PA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRayPA: Coding = {
  code: '36623-7',
  display: 'Hand - left X-ray PA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRayPA: Coding = {
  code: '36624-5',
  display: 'Wrist - bilateral X-ray PA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogram: Coding = {
  code: '36625-2',
  display: 'Breast Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogram: Coding = {
  code: '36626-0',
  display: 'Breast - bilateral Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogram: Coding = {
  code: '36627-8',
  display: 'Breast - left Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalXRay: Coding = {
  code: '36628-6',
  display: 'Internal auditory canal X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralXRay: Coding = {
  code: '36629-4',
  display: 'Hand - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRay: Coding = {
  code: '36630-2',
  display: 'Hand - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipLeftXRay: Coding = {
  code: '36631-0',
  display: 'Pelvis and Hip - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusLeftXRay: Coding = {
  code: '36632-8',
  display: 'Humerus - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointBilateralXRay: Coding = {
  code: '36633-6',
  display: 'Sacroiliac joint - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointLeftXRay: Coding = {
  code: '36634-4',
  display: 'Sacroiliac joint - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRay: Coding = {
  code: '36635-1',
  display: 'Knee - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay: Coding = {
  code: '36636-9',
  display: 'Knee - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MaxillaXRay: Coding = {
  code: '36637-7',
  display: 'Maxilla X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaBilateralXRay: Coding = {
  code: '36638-5',
  display: 'Patella - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaLeftXRay: Coding = {
  code: '36639-3',
  display: 'Patella - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalFluoroscopy: Coding = {
  code: '36640-1',
  display: 'Spine Cervical Fluoroscopy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRay2Views: Coding = {
  code: '36641-9',
  display: 'Abdomen X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogram2Views: Coding = {
  code: '36642-7',
  display: 'Breast - left Mammogram 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRay2Views: Coding = {
  code: '36643-5',
  display: 'Chest X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestFluoroscopy2Views: Coding = {
  code: '36644-3',
  display: 'Chest Fluoroscopy 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleXRay2Views: Coding = {
  code: '36645-0',
  display: 'Clavicle X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleLeftXRay2Views: Coding = {
  code: '36646-8',
  display: 'Clavicle - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CoccyxXRay2Views: Coding = {
  code: '36647-6',
  display: 'Coccyx X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowXRay2Views: Coding = {
  code: '36648-4',
  display: 'Elbow X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralXRay2Views: Coding = {
  code: '36649-2',
  display: 'Elbow - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRay2Views: Coding = {
  code: '36650-0',
  display: 'Elbow - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityXRay2Views: Coding = {
  code: '36651-8',
  display: 'Lower extremity X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurXRay2Views: Coding = {
  code: '36652-6',
  display: 'Femur X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurBilateralXRay2Views: Coding = {
  code: '36653-4',
  display: 'Femur - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurLeftXRay2Views: Coding = {
  code: '36654-2',
  display: 'Femur - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerXRay2Views: Coding = {
  code: '36655-9',
  display: 'Finger X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerLeftXRay2Views: Coding = {
  code: '36656-7',
  display: 'Finger - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralXRay2Views: Coding = {
  code: '36657-5',
  display: 'Foot - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusAndUlnaXRay2Views: Coding = {
  code: '36658-3',
  display: 'Radius and Ulna X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusBilateralAndUlnaBilateralXRay2Views: Coding = {
  code: '36659-1',
  display: 'Radius - bilateral and Ulna - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusLeftAndUlnaLeftXRay2Views: Coding = {
  code: '36660-9',
  display: 'Radius - left and Ulna.left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusXRay2Views: Coding = {
  code: '36661-7',
  display: 'Calcaneus X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusLeftXRay2Views: Coding = {
  code: '36662-5',
  display: 'Calcaneus - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRay2Views: Coding = {
  code: '36663-3',
  display: 'Hip X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRay2Views: Coding = {
  code: '36664-1',
  display: 'Hip - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointLeftXRay2Views: Coding = {
  code: '36665-8',
  display: 'Acromioclavicular joint - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaLeftXRay2Views: Coding = {
  code: '36666-6',
  display: 'Scapula - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRay2Views: Coding = {
  code: '36667-4',
  display: 'Shoulder - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRay2Views: Coding = {
  code: '36668-2',
  display: 'Shoulder - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRay2Views: Coding = {
  code: '36669-0',
  display: 'Spine Cervical X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRay2Views: Coding = {
  code: '36670-8',
  display: 'Spine Lumbar X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaBilateralAndFibulaBilateralXRay2Views: Coding = {
  code: '36671-6',
  display: 'Tibia - bilateral and Fibula - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaLeftAndFibulaLeftXRay2Views: Coding = {
  code: '36672-4',
  display: 'Tibia - left and Fibula - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesLeftXRay2Views: Coding = {
  code: '36673-2',
  display: 'Toes - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRay2ViewsPortable: Coding = {
  code: '36674-0',
  display: 'Spine Lumbar X-ray 2 views portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesXRay5Views: Coding = {
  code: '36675-7',
  display: 'Facial bones X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay5Views: Coding = {
  code: '36676-5',
  display: 'Knee - left X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRay5Views: Coding = {
  code: '36677-3',
  display: 'Shoulder - left X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRay6Views: Coding = {
  code: '36678-1',
  display: 'Knee - bilateral X-ray 6 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRay6Views: Coding = {
  code: '36679-9',
  display: 'Shoulder - left X-ray 6 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRay7Views: Coding = {
  code: '36680-7',
  display: 'Spine Cervical X-ray 7 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRay7Views: Coding = {
  code: '36681-5',
  display: 'Spine Lumbar X-ray 7 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRay8Views: Coding = {
  code: '36682-3',
  display: 'Knee - bilateral X-ray 8 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRay8Views: Coding = {
  code: '36683-1',
  display: 'Wrist - left X-ray 8 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRayAPAndLateral: Coding = {
  code: '36684-9',
  display: 'Ankle - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayAPAndLateral: Coding = {
  code: '36685-6',
  display: 'Ankle - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusBilateralXRayAPAndLateral: Coding = {
  code: '36686-4',
  display: 'Calcaneus - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPAndLateral: Coding = {
  code: '36687-2',
  display: 'Chest X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CoccyxXRayAPAndLateral: Coding = {
  code: '36688-0',
  display: 'Coccyx X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowXRayAPAndLateral: Coding = {
  code: '36689-8',
  display: 'Elbow X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralXRayAPAndLateral: Coding = {
  code: '36690-6',
  display: 'Elbow - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRayAPAndLateral: Coding = {
  code: '36691-4',
  display: 'Elbow - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityXRayAPAndLateral: Coding = {
  code: '36692-2',
  display: 'Lower extremity X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurXRayAPAndLateral: Coding = {
  code: '36693-0',
  display: 'Femur X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurBilateralXRayAPAndLateral: Coding = {
  code: '36694-8',
  display: 'Femur - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurLeftXRayAPAndLateral: Coding = {
  code: '36695-5',
  display: 'Femur - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralXRayAPAndLateral: Coding = {
  code: '36696-3',
  display: 'Foot - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRayAPAndLateral: Coding = {
  code: '36697-1',
  display: 'Foot - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusAndUlnaXRayAPAndLateral: Coding = {
  code: '36698-9',
  display: 'Radius and Ulna X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusBilateralAndUlnaBilateralXRayAPAndLateral: Coding = {
  code: '36699-7',
  display: 'Radius - bilateral and Ulna - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusLeftAndUlnaLeftXRayAPAndLateral: Coding = {
  code: '36700-3',
  display: 'Radius - left and Ulna.left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusLeftXRayAPAndLateral: Coding = {
  code: '36701-1',
  display: 'Calcaneus - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayAPAndLateral: Coding = {
  code: '36702-9',
  display: 'Hip X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralXRayAPAndLateral: Coding = {
  code: '36703-7',
  display: 'Hip - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayAPAndLateral: Coding = {
  code: '36704-5',
  display: 'Hip - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipXRayAPAndLateral: Coding = {
  code: '36705-2',
  display: 'Pelvis and Hip X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusXRayAPAndLateral: Coding = {
  code: '36706-0',
  display: 'Humerus X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusBilateralXRayAPAndLateral: Coding = {
  code: '36707-8',
  display: 'Humerus - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusLeftXRayAPAndLateral: Coding = {
  code: '36708-6',
  display: 'Humerus - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayAPAndLateral: Coding = {
  code: '36709-4',
  display: 'Knee X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayAPAndLateral: Coding = {
  code: '36710-2',
  display: 'Knee - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleXRayAPAndLateral: Coding = {
  code: '36711-0',
  display: 'Mandible X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaBilateralXRayAPAndLateral: Coding = {
  code: '36712-8',
  display: 'Patella - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaLeftXRayAPAndLateral: Coding = {
  code: '36713-6',
  display: 'Patella - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaBilateralXRayAPAndLateral: Coding = {
  code: '36714-4',
  display: 'Scapula - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaLeftXRayAPAndLateral: Coding = {
  code: '36715-1',
  display: 'Scapula - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAPAndLateral: Coding = {
  code: '36716-9',
  display: 'Shoulder - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaBilateralAndFibulaBilateralXRayAPAndLateral: Coding = {
  code: '36717-7',
  display: 'Tibia - bilateral and Fibula - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaLeftAndFibulaLeftXRayAPAndLateral: Coding = {
  code: '36718-5',
  display: 'Tibia - left and Fibula - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesLeftXRayAPAndLateral: Coding = {
  code: '36719-3',
  display: 'Toes - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRayAPAndLateralAndOblique: Coding = {
  code: '36720-1',
  display: 'Ankle - bilateral X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayAPAndLateralAndOblique: Coding = {
  code: '36721-9',
  display: 'Ankle - left X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowXRayAPAndLateralAndOblique: Coding = {
  code: '36722-7',
  display: 'Elbow X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralXRayAPAndLateralAndOblique: Coding = {
  code: '36723-5',
  display: 'Elbow - bilateral X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRayAPAndLateralAndOblique: Coding = {
  code: '36724-3',
  display: 'Elbow - left X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerXRayAPAndLateralAndOblique: Coding = {
  code: '36725-0',
  display: 'Finger X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerBilateralXRayAPAndLateralAndOblique: Coding = {
  code: '36726-8',
  display: 'Finger - bilateral X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerLeftXRayAPAndLateralAndOblique: Coding = {
  code: '36727-6',
  display: 'Finger - left X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayAPAndLateralAndOblique: Coding = {
  code: '36728-4',
  display: 'Foot X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralXRayAPAndLateralAndOblique: Coding = {
  code: '36729-2',
  display: 'Foot - bilateral X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRayAPAndLateralAndOblique: Coding = {
  code: '36730-0',
  display: 'Foot - left X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusXRayAPAndLateralAndOblique: Coding = {
  code: '36731-8',
  display: 'Calcaneus X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayAPAndLateralAndOblique: Coding = {
  code: '36732-6',
  display: 'Knee - bilateral X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayAPAndLateralAndOblique: Coding = {
  code: '36733-4',
  display: 'Knee - left X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPAndLateralAndOblique: Coding = {
  code: '36734-2',
  display: 'Spine Cervical X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPAndLateralAndOblique: Coding = {
  code: '36735-9',
  display: 'Spine Lumbar X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbLeftXRayAPAndLateralAndOblique: Coding = {
  code: '36736-7',
  display: 'Thumb - left X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesXRayLimited: Coding = {
  code: '36737-5',
  display: 'Facial bones X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleXRayLimited: Coding = {
  code: '36738-3',
  display: 'Mandible X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRayLimited: Coding = {
  code: '36739-1',
  display: 'Wrist - bilateral X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralXRayOblique: Coding = {
  code: '36740-9',
  display: 'Elbow - bilateral X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRayOblique: Coding = {
  code: '36741-7',
  display: 'Elbow - left X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusBilateralAndUlnaBilateralXRayOblique: Coding = {
  code: '36742-5',
  display: 'Radius - bilateral and Ulna - bilateral X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusLeftAndUlnaLeftXRayOblique: Coding = {
  code: '36743-3',
  display: 'Radius - left and Ulna.left X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusLeftXRayOblique: Coding = {
  code: '36744-1',
  display: 'Humerus - left X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayOblique: Coding = {
  code: '36745-8',
  display: 'Knee - bilateral X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayOblique: Coding = {
  code: '36746-6',
  display: 'Knee - left X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleXRayOblique: Coding = {
  code: '36747-4',
  display: 'Mandible X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayOblique: Coding = {
  code: '36748-2',
  display: 'Spine Cervical X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaLeftAndFibulaLeftXRayOblique: Coding = {
  code: '36749-0',
  display: 'Tibia - left and Fibula - left X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndAPLateralDecubitus: Coding = {
  code: '36750-8',
  display: 'Chest X-ray PA and AP lateral-decubitus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestFluoroscopyPAAndLateral: Coding = {
  code: '36751-6',
  display: 'Chest Fluoroscopy PA and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralXRayPAAndLateral: Coding = {
  code: '36752-4',
  display: 'Hand - bilateral X-ray PA and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRayPAAndLateral: Coding = {
  code: '36753-2',
  display: 'Hand - left X-ray PA and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleXRayPAAndLateral: Coding = {
  code: '36754-0',
  display: 'Mandible X-ray PA and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRayPAAndLateralAndOblique: Coding = {
  code: '36755-7',
  display: 'Hand X-ray PA and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralXRayPAAndLateralAndOblique: Coding = {
  code: '36756-5',
  display: 'Hand - bilateral X-ray PA and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRayPAAndLateralAndOblique: Coding = {
  code: '36757-3',
  display: 'Hand - left X-ray PA and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateralAndObliqueAndLordotic: Coding = {
  code: '36758-1',
  display: 'Chest X-ray PA and lateral and oblique and lordotic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLordotic: Coding = {
  code: '36759-9',
  display: 'Chest X-ray PA and lordotic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AVShuntFluoroscopicAngiogramAngioplastyWContrast: Coding = {
  code: '36760-7',
  display: 'AV shunt Fluoroscopic angiogram Angioplasty W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BiliaryDuctsFluoroscopyBalloonDilatationWContrast: Coding = {
  code: '36761-5',
  display: 'Biliary ducts Fluoroscopy Balloon dilatation W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVesselFluoroscopicAngiogramAngioplastyWContrast: Coding = {
  code: '36762-3',
  display: 'Extremity vessel Fluoroscopic angiogram Angioplasty W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralArteryAndPoplitealArteryFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '36763-1',
  display: 'Femoral artery and Popliteal artery Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralVesselAndPoplitealArteryFluoroscopicAngiogramAtherectomyWContrast: Coding = {
  code: '36764-9',
  display: 'Femoral vessel and Popliteal artery Fluoroscopic angiogram Atherectomy W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VesselFluoroscopicAngiogramAtherectomyWContrast: Coding = {
  code: '36765-6',
  display: 'Vessel Fluoroscopic angiogram Atherectomy W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CoronaryArteriesFluoroscopicAngiogramAtherectomyWContrastIA: Coding = {
  code: '36766-4',
  display: 'Coronary arteries Fluoroscopic angiogram Atherectomy W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfAdrenalGland: Coding = {
  code: '36767-2',
  display: 'CT Guidance for biopsy of Adrenal gland',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfMuscle: Coding = {
  code: '36768-0',
  display: 'CT Guidance for biopsy of Muscle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForChangeOfNephrostomyTubeInKidney: Coding = {
  code: '36769-8',
  display: 'CT Guidance for change of nephrostomy tube in Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfBiliaryDuctsAndGallbladder: Coding = {
  code: '36770-6',
  display: 'CT Guidance for drainage of Biliary ducts and Gallbladder',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfJoint: Coding = {
  code: '36771-4',
  display: 'Fluoroscopy Guidance for injection of Joint',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForPlacementOfNephrostomyTubeInKidney: Coding = {
  code: '36772-2',
  display: 'CT Guidance for placement of nephrostomy tube in Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporalBoneCT: Coding = {
  code: '36773-0',
  display: 'Temporal bone CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointLeftMRI: Coding = {
  code: '36774-8',
  display: 'Upper extremity joint - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointRightMRI: Coding = {
  code: '36775-5',
  display: 'Upper extremity joint - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidXRayTomograph: Coding = {
  code: '36776-3',
  display: 'Mastoid X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitMRI: Coding = {
  code: '36777-1',
  display: 'Orbit MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitRightMRI: Coding = {
  code: '36778-9',
  display: 'Orbit - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OvaryMRI: Coding = {
  code: '36779-7',
  display: 'Ovary MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToeMRI: Coding = {
  code: '36780-5',
  display: 'Toe MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalVeinsMRIAngiogram: Coding = {
  code: '36781-3',
  display: 'Abdominal veins MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SubclavianArteryMRIAngiogram: Coding = {
  code: '36782-1',
  display: 'Subclavian artery MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinsMRIAngiogram: Coding = {
  code: '36783-9',
  display: 'Veins MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinsLeftMRIAngiogram: Coding = {
  code: '36784-7',
  display: 'Lower extremity veins - left MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinsRightMRIAngiogram: Coding = {
  code: '36785-4',
  display: 'Lower extremity veins - right MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinsLeftMRIAngiogram: Coding = {
  code: '36786-2',
  display: 'Upper extremity veins - left MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinsRightMRIAngiogram: Coding = {
  code: '36787-0',
  display: 'Upper extremity veins - right MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckVeinsMRIAngiogram: Coding = {
  code: '36788-8',
  display: 'Neck veins MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisVeinsMRIAngiogram: Coding = {
  code: '36789-6',
  display: 'Pelvis veins MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VenaCavaInferiorAndLowerExtremityVeinsMRIAngiogram: Coding = {
  code: '36790-4',
  display: 'Vena cava.inferior and Lower extremity veins MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalVesselsMRIAngiogram: Coding = {
  code: '36791-2',
  display: 'Abdominal vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalVesselsMRIAngiogram: Coding = {
  code: '36792-0',
  display: 'Adrenal vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidVesselMRIAngiogram: Coding = {
  code: '36793-8',
  display: 'Carotid vessel MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVesselsMRIAngiogram: Coding = {
  code: '36794-6',
  display: 'Extremity vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsLeftMRIAngiogram: Coding = {
  code: '36795-3',
  display: 'Lower extremity vessels - left MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsRightMRIAngiogram: Coding = {
  code: '36796-1',
  display: 'Lower extremity vessels - right MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsLeftMRIAngiogram: Coding = {
  code: '36797-9',
  display: 'Upper extremity vessels - left MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsRightMRIAngiogram: Coding = {
  code: '36798-7',
  display: 'Upper extremity vessels - right MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeVesselsMRIAngiogram: Coding = {
  code: '36799-5',
  display: 'Knee vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeVesselsLeftMRIAngiogram: Coding = {
  code: '36800-1',
  display: 'Knee vessels - left MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeVesselsRightMRIAngiogram: Coding = {
  code: '36801-9',
  display: 'Knee vessels - right MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitVesselsMRIAngiogram: Coding = {
  code: '36802-7',
  display: 'Orbit vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryVesselsMRIAngiogram: Coding = {
  code: '36803-5',
  display: 'Pulmonary vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsBilateralMRIAngiogram: Coding = {
  code: '36804-3',
  display: 'Renal vessels - bilateral MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderVesselsMRIAngiogram: Coding = {
  code: '36805-0',
  display: 'Shoulder vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderVesselsLeftMRIAngiogram: Coding = {
  code: '36806-8',
  display: 'Shoulder vessels - left MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderVesselsRightMRIAngiogram: Coding = {
  code: '36807-6',
  display: 'Shoulder vessels - right MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVesselsMRIAngiogramLimited: Coding = {
  code: '36808-4',
  display: 'Head vessels MRI angiogram limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HepaticArteryCTAngiogramWContrastIA: Coding = {
  code: '36809-2',
  display: 'Hepatic artery CT angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperJointCTWContrastIntraarticular: Coding = {
  code: '36810-0',
  display: 'Upper Joint CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JointCTWContrastIntraarticular: Coding = {
  code: '36811-8',
  display: 'Joint CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JointMRIWContrastIntraarticular: Coding = {
  code: '36812-6',
  display: 'Joint MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenAndPelvisCTWContrastIV: Coding = {
  code: '36813-4',
  display: 'Abdomen and Pelvis CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadArteriesCTAngiogramWContrastIV: Coding = {
  code: '36814-2',
  display: 'Head arteries CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporalBoneCTWContrastIV: Coding = {
  code: '36815-9',
  display: 'Temporal bone CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporalBoneRightCTWContrastIV: Coding = {
  code: '36816-7',
  display: 'Temporal bone - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointBilateralMRIWContrastIV: Coding = {
  code: '36817-5',
  display: 'Upper extremity joint - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointLeftMRIWContrastIV: Coding = {
  code: '36818-3',
  display: 'Upper extremity joint - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointRightMRIWContrastIV: Coding = {
  code: '36819-1',
  display: 'Upper extremity joint - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitMRIWContrastIV: Coding = {
  code: '36820-9',
  display: 'Orbit MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitLeftMRIWContrastIV: Coding = {
  code: '36821-7',
  display: 'Orbit - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitRightMRIWContrastIV: Coding = {
  code: '36822-5',
  display: 'Orbit - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OvaryMRIWContrastIV: Coding = {
  code: '36823-3',
  display: 'Ovary MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinsLeftCTWContrastIV: Coding = {
  code: '36824-1',
  display: 'Lower extremity veins - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinsRightCTWContrastIV: Coding = {
  code: '36825-8',
  display: 'Lower extremity veins - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVeinsMRIAngiogramWContrastIV: Coding = {
  code: '36826-6',
  display: 'Head veins MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckVeinsMRIAngiogramWContrastIV: Coding = {
  code: '36827-4',
  display: 'Neck veins MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalVesselsCTAngiogramWContrastIV: Coding = {
  code: '36828-2',
  display: 'Abdominal vessels CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidVesselMRIAngiogramWContrastIV: Coding = {
  code: '36829-0',
  display: 'Carotid vessel MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVesselsCTAngiogramWContrastIV: Coding = {
  code: '36830-8',
  display: 'Head vessels CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsCTAngiogramWContrastIV: Coding = {
  code: '36831-6',
  display: 'Lower extremity vessels CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitVesselsMRIAngiogramWContrastIV: Coding = {
  code: '36832-4',
  display: 'Orbit vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsCTAngiogramWContrastIV: Coding = {
  code: '36833-2',
  display: 'Renal vessels CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VesselCTAngiogramWContrastIV: Coding = {
  code: '36834-0',
  display: 'Vessel CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PetrousBoneCTWAndWOContrastIV: Coding = {
  code: '36835-7',
  display: 'Petrous bone CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporalBoneCTWAndWOContrastIV: Coding = {
  code: '36837-3',
  display: 'Temporal bone CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidXRay3Views: Coding = {
  code: '36838-1',
  display: 'Mastoid X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidXRay4Views: Coding = {
  code: '36839-9',
  display: 'Mastoid X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointLeftMRIWAndWOContrastIV: Coding = {
  code: '36840-7',
  display: 'Upper extremity joint - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointRightMRIWAndWOContrastIV: Coding = {
  code: '36841-5',
  display: 'Upper extremity joint - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitMRIWAndWOContrastIV: Coding = {
  code: '36842-3',
  display: 'Orbit MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitLeftMRIWAndWOContrastIV: Coding = {
  code: '36843-1',
  display: 'Orbit - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitRightMRIWAndWOContrastIV: Coding = {
  code: '36844-9',
  display: 'Orbit - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OvaryMRIWAndWOContrastIV: Coding = {
  code: '36845-6',
  display: 'Ovary MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalVeinsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36846-4',
  display: 'Abdominal veins MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVeinsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36847-2',
  display: 'Head veins MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestVeinsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36848-0',
  display: 'Chest veins MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinsLeftMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36849-8',
  display: 'Lower extremity veins - left MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinsRightMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36850-6',
  display: 'Lower extremity veins - right MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinsLeftMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36851-4',
  display: 'Upper extremity veins - left MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinsRightMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36852-2',
  display: 'Upper extremity veins - right MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckVeinsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36853-0',
  display: 'Neck veins MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisVeinsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36854-8',
  display: 'Pelvis veins MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36855-5',
  display: 'Abdominal vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidVesselMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36856-3',
  display: 'Carotid vessel MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36857-1',
  display: 'Head vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsLeftMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36858-9',
  display: 'Lower extremity vessels - left MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsRightMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36859-7',
  display: 'Lower extremity vessels - right MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsLeftMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36860-5',
  display: 'Upper extremity vessels - left MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsRightMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36861-3',
  display: 'Upper extremity vessels - right MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeVesselsRightMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36862-1',
  display: 'Knee vessels - right MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36863-9',
  display: 'Pelvis vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderVesselsLeftMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36864-7',
  display: 'Shoulder vessels - left MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderVesselsRightMRIAngiogramWAndWOContrastIV: Coding = {
  code: '36865-4',
  display: 'Shoulder vessels - right MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporalBoneCTWOContrast: Coding = {
  code: '36866-2',
  display: 'Temporal bone CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporalBoneLeftCTWOContrast: Coding = {
  code: '36867-0',
  display: 'Temporal bone - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporalBoneRightCTWOContrast: Coding = {
  code: '36868-8',
  display: 'Temporal bone - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointLeftMRIWOContrast: Coding = {
  code: '36869-6',
  display: 'Upper extremity joint - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityJointRightMRIWOContrast: Coding = {
  code: '36870-4',
  display: 'Upper extremity joint - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JointMRIWOContrast: Coding = {
  code: '36871-2',
  display: 'Joint MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitMRIWOContrast: Coding = {
  code: '36872-0',
  display: 'Orbit MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitLeftMRIWOContrast: Coding = {
  code: '36873-8',
  display: 'Orbit - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitRightMRIWOContrast: Coding = {
  code: '36874-6',
  display: 'Orbit - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OvaryMRIWOContrast: Coding = {
  code: '36875-3',
  display: 'Ovary MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVeinsMRIAngiogramWOContrast: Coding = {
  code: '36876-1',
  display: 'Head veins MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckVeinsMRIAngiogramWOContrast: Coding = {
  code: '36877-9',
  display: 'Neck veins MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalVesselsMRIAngiogramWOContrast: Coding = {
  code: '36878-7',
  display: 'Abdominal vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleVesselsMRIAngiogramWOContrast: Coding = {
  code: '36879-5',
  display: 'Ankle vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidVesselMRIAngiogramWOContrast: Coding = {
  code: '36880-3',
  display: 'Carotid vessel MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVesselsMRIAngiogramWOContrast: Coding = {
  code: '36881-1',
  display: 'Head vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsLeftMRIAngiogramWOContrast: Coding = {
  code: '36882-9',
  display: 'Lower extremity vessels - left MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisVesselsMRIAngiogramWOContrast: Coding = {
  code: '36883-7',
  display: 'Pelvis vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitXRay: Coding = {
  code: '36886-0',
  display: 'Orbit X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitLeftXRay: Coding = {
  code: '36887-8',
  display: 'Orbit - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidXRay5Views: Coding = {
  code: '36890-2',
  display: 'Mastoid X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidXRayLimited: Coding = {
  code: '36893-6',
  display: 'Mastoid X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaAndFibulaXRayOblique: Coding = {
  code: '36894-4',
  display: 'Tibia and Fibula X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationAndPlacementOfDrainageTubeOfAbdomen: Coding = {
  code: '36926-4',
  display: 'CT Guidance for aspiration and placement of drainage tube of Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfFacialBonesAndMaxilla: Coding = {
  code: '36927-2',
  display: 'CT Guidance for biopsy of Facial bones and Maxilla',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceStereotacticForBiopsyOfHead: Coding = {
  code: '36928-0',
  display: 'CT Guidance.stereotactic for biopsy of Head',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceStereotacticForBiopsyOfHeadWOContrast: Coding = {
  code: '36929-8',
  display: 'CT Guidance.stereotactic for biopsy of Head-- WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalGlandCT: Coding = {
  code: '36930-6',
  display: 'Adrenal gland CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalGlandMRI: Coding = {
  code: '36931-4',
  display: 'Adrenal gland MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PituitaryAndSellaTurcicaCT: Coding = {
  code: '36932-2',
  display: 'Pituitary and Sella turcica CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SalivaryGlandMRI: Coding = {
  code: '36933-0',
  display: 'Salivary gland MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartCTForScoring: Coding = {
  code: '36934-8',
  display: 'Heart CT for scoring',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartCTForScoringWContrastIV: Coding = {
  code: '36935-5',
  display: 'Heart CT for scoring W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceStereotacticForBiopsyOfBrain: Coding = {
  code: '36936-3',
  display: 'MRI Guidance.stereotactic for biopsy of Brain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesAndMaxillaCTLimited: Coding = {
  code: '36937-1',
  display: 'Facial bones and Maxilla CT limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesAndMaxillaCTLimitedWOContrast: Coding = {
  code: '36938-9',
  display: 'Facial bones and Maxilla CT limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCTStereotactic: Coding = {
  code: '36939-7',
  display: 'Spine CT stereotactic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCTStereotactic: Coding = {
  code: '36940-5',
  display: 'Unspecified body region CT stereotactic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SalivaryGlandCTWContrastIntraSalivaryDuct: Coding = {
  code: '36941-3',
  display: 'Salivary gland CT W contrast intra salivary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenMRIWContrastIV: Coding = {
  code: '36942-1',
  display: 'Chest and Abdomen MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalGlandCTWContrastIV: Coding = {
  code: '36943-9',
  display: 'Adrenal gland CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BiliaryDuctsAndPancreaticDuctMRIWAndWOContrastIV: Coding = {
  code: '36944-7',
  display: 'Biliary ducts and Pancreatic duct MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRay2ViewsStanding: Coding = {
  code: '36945-4',
  display: 'Knee - bilateral X-ray 2 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRay2ViewsStanding: Coding = {
  code: '36946-2',
  display: 'Spine Lumbar X-ray 2 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralXRay3ViewsStanding: Coding = {
  code: '36947-0',
  display: 'Foot - bilateral X-ray 3 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRay3ViewsStanding: Coding = {
  code: '36948-8',
  display: 'Foot - left X-ray 3 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRay3ViewsStanding: Coding = {
  code: '36949-6',
  display: 'Spine Lumbar X-ray 3 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalGlandCTWAndWOContrastIV: Coding = {
  code: '36950-4',
  display: 'Adrenal gland CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalGlandMRIWAndWOContrastIV: Coding = {
  code: '36951-2',
  display: 'Adrenal gland MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenAndPelvisCTWOContrast: Coding = {
  code: '36952-0',
  display: 'Abdomen and Pelvis CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalGlandCTWOContrast: Coding = {
  code: '36953-8',
  display: 'Adrenal gland CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalGlandMRIWOContrast: Coding = {
  code: '36954-6',
  display: 'Adrenal gland MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidCTWOContrast: Coding = {
  code: '36955-3',
  display: 'Thyroid CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitAndFaceMRIWOContrast: Coding = {
  code: '36956-1',
  display: 'Orbit and Face MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesAndMaxillaCTAnd3DReconstruction: Coding = {
  code: '36957-9',
  display: 'Facial bones and Maxilla CT and 3D reconstruction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsBilateralXRayAPSingleView: Coding = {
  code: '36958-7',
  display: 'Ribs - bilateral X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLeftXRayAPSingleView: Coding = {
  code: '36959-5',
  display: 'Ribs - left X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPUprightPortable: Coding = {
  code: '36960-3',
  display: 'Chest X-ray AP upright portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPAndWestPointAndOutlet: Coding = {
  code: '36961-1',
  display: 'Shoulder - left X-ray AP and West Point and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogramAxillary: Coding = {
  code: '36962-9',
  display: 'Breast Mammogram axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAxillary: Coding = {
  code: '36963-7',
  display: 'Shoulder - bilateral X-ray axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAxillary: Coding = {
  code: '36964-5',
  display: 'Shoulder - left X-ray axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRayBallCatcher: Coding = {
  code: '36965-2',
  display: 'Hand X-ray Ball Catcher',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralXRayBrewerton: Coding = {
  code: '36966-0',
  display: 'Hand - bilateral X-ray Brewerton',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRayBrewerton: Coding = {
  code: '36967-8',
  display: 'Hand - left X-ray Brewerton',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRayWClenchedFist: Coding = {
  code: '36968-6',
  display: 'Wrist - bilateral X-ray W clenched fist',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayLateralWExtension: Coding = {
  code: '36971-0',
  display: 'Wrist - left X-ray lateral W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayLateralWFlexion: Coding = {
  code: '36972-8',
  display: 'Wrist - left X-ray lateral W flexion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayFriedman: Coding = {
  code: '36973-6',
  display: 'Hip X-ray Friedman',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayGarth: Coding = {
  code: '36974-4',
  display: 'Shoulder - left X-ray Garth',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusBilateralXRayHarris: Coding = {
  code: '36975-1',
  display: 'Calcaneus - bilateral X-ray Harris',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayHarris: Coding = {
  code: '36976-9',
  display: 'Foot X-ray Harris',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusLeftXRayHarris: Coding = {
  code: '36977-7',
  display: 'Calcaneus - left X-ray Harris',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayHolmblad: Coding = {
  code: '36978-5',
  display: 'Knee X-ray Holmblad',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowXRayJones: Coding = {
  code: '36979-3',
  display: 'Elbow X-ray Jones',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRayJones: Coding = {
  code: '36980-1',
  display: 'Elbow - left X-ray Jones',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayJudet: Coding = {
  code: '36981-9',
  display: 'Hip X-ray Judet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralXRayJudet: Coding = {
  code: '36982-7',
  display: 'Hip - bilateral X-ray Judet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayJudet: Coding = {
  code: '36983-5',
  display: 'Hip - left X-ray Judet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayLateralCrosstable: Coding = {
  code: '36984-3',
  display: 'Abdomen X-ray lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayLateralCrosstable: Coding = {
  code: '36985-0',
  display: 'Hip X-ray lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralXRayLateralCrosstable: Coding = {
  code: '36986-8',
  display: 'Hip - bilateral X-ray lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayLateralCrosstable: Coding = {
  code: '36987-6',
  display: 'Hip - left X-ray lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayLateralCrosstable: Coding = {
  code: '36988-4',
  display: 'Knee X-ray lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayLateralCrosstable: Coding = {
  code: '36989-2',
  display: 'Spine Cervical X-ray lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayLateralCrosstable: Coding = {
  code: '36990-0',
  display: 'Spine Lumbar X-ray lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayLateralCrosstablePortable: Coding = {
  code: '36991-8',
  display: 'Spine Cervical X-ray lateral crosstable portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayLateralCrosstablePortable: Coding = {
  code: '36992-6',
  display: 'Spine Lumbar X-ray lateral crosstable portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralXRayLateralFrog: Coding = {
  code: '36993-4',
  display: 'Hip - bilateral X-ray lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayLateralFrog: Coding = {
  code: '36994-2',
  display: 'Hip - left X-ray lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayLeftLateral: Coding = {
  code: '36995-9',
  display: 'Abdomen X-ray left lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayRightLateral: Coding = {
  code: '36996-7',
  display: 'Abdomen X-ray right lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayLateralWExtension: Coding = {
  code: '36997-5',
  display: 'Spine Cervical X-ray lateral W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayLateralWFlexion: Coding = {
  code: '36998-3',
  display: 'Spine Cervical X-ray lateral W flexion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayLateralHyperextension: Coding = {
  code: '36999-1',
  display: 'Knee - bilateral X-ray lateral hyperextension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayLateralHyperextension: Coding = {
  code: '37000-7',
  display: 'Knee - left X-ray lateral hyperextension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayLateralStanding: Coding = {
  code: '37001-5',
  display: 'Foot X-ray lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayLateralStanding: Coding = {
  code: '37002-3',
  display: 'Knee - left X-ray lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayLateralStanding: Coding = {
  code: '37003-1',
  display: 'Spine Lumbar X-ray lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayLaurin: Coding = {
  code: '37004-9',
  display: 'Knee X-ray Laurin',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramMagnification: Coding = {
  code: '37005-6',
  display: 'Breast - left Mammogram magnification',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramMLO: Coding = {
  code: '37006-4',
  display: 'Breast - bilateral Mammogram MLO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRayMortise: Coding = {
  code: '37007-2',
  display: 'Ankle X-ray Mortise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayLeftOblique: Coding = {
  code: '37008-0',
  display: 'Chest X-ray left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayLeftOblique: Coding = {
  code: '37009-8',
  display: 'Spine Lumbar X-ray left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayRightOblique: Coding = {
  code: '37010-6',
  display: 'Chest X-ray right oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayRightOblique: Coding = {
  code: '37011-4',
  display: 'Spine Lumbar X-ray right oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayOutlet: Coding = {
  code: '37012-2',
  display: 'Shoulder - bilateral X-ray outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayOutlet: Coding = {
  code: '37013-0',
  display: 'Shoulder - left X-ray outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayPAStanding: Coding = {
  code: '37014-8',
  display: 'Knee - left X-ray PA standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayPAProne: Coding = {
  code: '37015-5',
  display: 'Abdomen X-ray PA prone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramRoll: Coding = {
  code: '37016-3',
  display: 'Breast - bilateral Mammogram roll',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramRoll: Coding = {
  code: '37017-1',
  display: 'Breast - left Mammogram roll',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayRosenbergStanding: Coding = {
  code: '37018-9',
  display: 'Knee X-ray Rosenberg standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayRosenbergStanding: Coding = {
  code: '37019-7',
  display: 'Knee - left X-ray Rosenberg standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayRosenbergStanding: Coding = {
  code: '37020-5',
  display: 'Knee - bilateral X-ray Rosenberg standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusBilateralXRaySkiJump: Coding = {
  code: '37021-3',
  display: 'Calcaneus - bilateral X-ray ski jump',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusXRaySkiJump: Coding = {
  code: '37022-1',
  display: 'Calcaneus X-ray ski jump',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusLeftXRaySkiJump: Coding = {
  code: '37023-9',
  display: 'Calcaneus - left X-ray ski jump',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayStrykerNotch: Coding = {
  code: '37024-7',
  display: 'Shoulder - bilateral X-ray Stryker Notch',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayStrykerNotch: Coding = {
  code: '37025-4',
  display: 'Shoulder - left X-ray Stryker Notch',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRaySubmentovertex: Coding = {
  code: '37026-2',
  display: 'Skull X-ray submentovertex',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRaySunrise: Coding = {
  code: '37027-0',
  display: 'Knee - bilateral X-ray Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogramTangential: Coding = {
  code: '37028-8',
  display: 'Breast Mammogram tangential',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramTangential: Coding = {
  code: '37029-6',
  display: 'Breast - bilateral Mammogram tangential',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramTangential: Coding = {
  code: '37030-4',
  display: 'Breast - left Mammogram tangential',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusXRayTransthoracic: Coding = {
  code: '37031-2',
  display: 'Humerus X-ray transthoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusBilateralXRayTransthoracic: Coding = {
  code: '37032-0',
  display: 'Humerus - bilateral X-ray transthoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusLeftXRayTransthoracic: Coding = {
  code: '37033-8',
  display: 'Humerus - left X-ray transthoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayTransthoracic: Coding = {
  code: '37034-6',
  display: 'Shoulder - left X-ray transthoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayGrashey: Coding = {
  code: '37035-3',
  display: 'Shoulder - bilateral X-ray Grashey',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogramTrueLateral: Coding = {
  code: '37037-9',
  display: 'Breast Mammogram true lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramTrueLateral: Coding = {
  code: '37038-7',
  display: 'Breast - bilateral Mammogram true lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayTrueLateral: Coding = {
  code: '37039-5',
  display: 'Hip X-ray true lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayTrueLateral: Coding = {
  code: '37040-3',
  display: 'Hip - left X-ray true lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayTunnel: Coding = {
  code: '37041-1',
  display: 'Knee - bilateral X-ray tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayTunnel: Coding = {
  code: '37042-9',
  display: 'Knee - left X-ray tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayTunnelStanding: Coding = {
  code: '37043-7',
  display: 'Knee - left X-ray tunnel standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayUlnarDeviation: Coding = {
  code: '37044-5',
  display: 'Wrist - left X-ray ulnar deviation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRayUlnarVariance: Coding = {
  code: '37045-2',
  display: 'Wrist - bilateral X-ray ulnar variance',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayUpright: Coding = {
  code: '37046-0',
  display: 'Abdomen X-ray upright',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayVelpeauAxillary: Coding = {
  code: '37047-8',
  display: 'Shoulder - bilateral X-ray Velpeau axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayVelpeauAxillary: Coding = {
  code: '37048-6',
  display: 'Shoulder - left X-ray Velpeau axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayVonRossen: Coding = {
  code: '37049-4',
  display: 'Hip X-ray Von rossen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayWestPoint: Coding = {
  code: '37050-2',
  display: 'Shoulder - bilateral X-ray West Point',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayWestPoint: Coding = {
  code: '37051-0',
  display: 'Shoulder - left X-ray West Point',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramXCCL: Coding = {
  code: '37052-8',
  display: 'Breast - bilateral Mammogram XCCL',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramXCCL: Coding = {
  code: '37053-6',
  display: 'Breast - left Mammogram XCCL',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaLeftXRayY: Coding = {
  code: '37054-4',
  display: 'Scapula - left X-ray Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaBilateralXRayY: Coding = {
  code: '37055-1',
  display: 'Scapula - bilateral X-ray Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointBilateralXRayZanca: Coding = {
  code: '37056-9',
  display: 'Acromioclavicular joint - bilateral X-ray Zanca',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointLeftXRayZanca: Coding = {
  code: '37057-7',
  display: 'Acromioclavicular joint - left X-ray Zanca',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusBilateralXRayStanding: Coding = {
  code: '37058-5',
  display: 'Calcaneus - bilateral X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralXRayStanding: Coding = {
  code: '37059-3',
  display: 'Hip - bilateral X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FetalXRay: Coding = {
  code: '37060-1',
  display: 'Fetal X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusBilateralXRay: Coding = {
  code: '37062-7',
  display: 'Humerus - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyOfForeignBody: Coding = {
  code: '37063-5',
  display: 'Unspecified body region Fluoroscopy of foreign body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcetabulumLeftXRay2Views: Coding = {
  code: '37064-3',
  display: 'Acetabulum - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLeftXRay2Views: Coding = {
  code: '37066-8',
  display: 'Ribs - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRay2ViewsWNippleMarkers: Coding = {
  code: '37067-6',
  display: 'Chest X-ray 2 views W nipple markers',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralXRay2ViewsStanding: Coding = {
  code: '37068-4',
  display: 'Foot - bilateral X-ray 2 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRay2ViewsStanding: Coding = {
  code: '37069-2',
  display: 'Foot - left X-ray 2 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRay4Views: Coding = {
  code: '37070-0',
  display: 'Wrist - bilateral X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRay4Views: Coding = {
  code: '37071-8',
  display: 'Wrist - left X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRay5Views: Coding = {
  code: '37072-6',
  display: 'Wrist - left X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRay5ViewsStanding: Coding = {
  code: '37073-4',
  display: 'Spine Lumbar X-ray 5 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRay6Views: Coding = {
  code: '37074-2',
  display: 'Wrist - left X-ray 6 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayAPPortable: Coding = {
  code: '37075-9',
  display: 'Hip X-ray AP portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPSupineAndLateralDecubitusPortable: Coding = {
  code: '37076-7',
  display: 'Abdomen X-ray AP (supine and lateral-decubitus) portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayAPAndLateralCrosstablePortable: Coding = {
  code: '37077-5',
  display: 'Hip X-ray AP and lateral crosstable portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPAndLateralPortable: Coding = {
  code: '37078-3',
  display: 'Spine Lumbar X-ray AP and lateral portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPAndLateralAndOdontoidPortable: Coding = {
  code: '37079-1',
  display: 'Spine Cervical X-ray AP and lateral and odontoid portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAPAndAxillary: Coding = {
  code: '37080-9',
  display: 'Shoulder - bilateral X-ray AP and axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAPAndAxillaryAndOutlet: Coding = {
  code: '37081-7',
  display: 'Shoulder - bilateral X-ray AP and axillary and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPAndAxillaryAndOutlet: Coding = {
  code: '37082-5',
  display: 'Shoulder - left X-ray AP and axillary and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPAndAxillaryAndOutletAndZanca: Coding = {
  code: '37083-3',
  display: 'Shoulder - left X-ray AP and axillary and outlet and Zanca',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPAndAxillaryAndY: Coding = {
  code: '37084-1',
  display: 'Shoulder - left X-ray AP and axillary and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPSupineAndLateralDecubitus: Coding = {
  code: '37085-8',
  display: 'Abdomen X-ray AP (supine and lateral-decubitus)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayAPAndLateralCrosstable: Coding = {
  code: '37086-6',
  display: 'Hip X-ray AP and lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayAPAndLateralCrosstable: Coding = {
  code: '37087-4',
  display: 'Hip - left X-ray AP and lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipLeftXRayAPAndLateralCrosstable: Coding = {
  code: '37088-2',
  display: 'Pelvis and Hip - left X-ray AP and lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipXRayAPAndLateralCrosstable: Coding = {
  code: '37089-0',
  display: 'Pelvis and Hip X-ray AP and lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayAPAndLateralCrosstable: Coding = {
  code: '37090-8',
  display: 'Knee X-ray AP and lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayAPAndLateralFrog: Coding = {
  code: '37091-6',
  display: 'Hip X-ray AP and lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralXRayAPAndLateralFrog: Coding = {
  code: '37092-4',
  display: 'Hip - bilateral X-ray AP and lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayAPAndLateralFrog: Coding = {
  code: '37093-2',
  display: 'Hip - left X-ray AP and lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipLeftXRayAPAndLateralFrog: Coding = {
  code: '37094-0',
  display: 'Pelvis and Hip - left X-ray AP and lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRayAPAndLateralAndMortise: Coding = {
  code: '37095-7',
  display: 'Ankle X-ray AP and lateral and Mortise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRayAPAndLateralAndMortise: Coding = {
  code: '37096-5',
  display: 'Ankle - bilateral X-ray AP and lateral and Mortise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayAPAndLateralAndMortise: Coding = {
  code: '37097-3',
  display: 'Ankle - left X-ray AP and lateral and Mortise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPAndObliqueAndLateralWFlexionAndWExtension: Coding = {
  code: '37098-1',
  display: 'Spine Cervical X-ray AP and oblique and lateral W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPAndLateralAndObliqueAndOdontoid: Coding = {
  code: '37099-9',
  display: 'Spine Cervical X-ray AP and lateral and oblique and odontoid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPAndObliqueAndOdontoidAndLateralWFlexionAndWExtension: Coding = {
  code: '37100-5',
  display: 'Spine Cervical X-ray AP and oblique and odontoid and lateral W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPAndLateralAndObliqueAndSpot: Coding = {
  code: '37101-3',
  display: 'Spine Lumbar X-ray AP and lateral and oblique and spot',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayAPAndLateralAndObliqueAndSunrise: Coding = {
  code: '37102-1',
  display: 'Knee - bilateral X-ray AP and lateral and oblique and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPAndLateralAndOdontoid: Coding = {
  code: '37103-9',
  display: 'Spine Cervical X-ray AP and lateral and odontoid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPAndOdontoidAndLateralWFlexionAndWExtension: Coding = {
  code: '37104-7',
  display: 'Spine Cervical X-ray AP and odontoid and lateral W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPAndLateralAndSpot: Coding = {
  code: '37105-4',
  display: 'Spine Lumbar X-ray AP and lateral and spot',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayAPAndLateralAndSunrise: Coding = {
  code: '37106-2',
  display: 'Knee X-ray AP and lateral and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayAPAndLateralAndSunrise: Coding = {
  code: '37107-0',
  display: 'Knee - bilateral X-ray AP and lateral and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayAPAndLateralAndSunrise: Coding = {
  code: '37108-8',
  display: 'Knee - left X-ray AP and lateral and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaBilateralXRayAPAndLateralAndSunrise: Coding = {
  code: '37109-6',
  display: 'Patella - bilateral X-ray AP and lateral and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaLeftXRayAPAndLateralAndSunrise: Coding = {
  code: '37110-4',
  display: 'Patella - left X-ray AP and lateral and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayAPAndLateralAndSunriseAndTunnel: Coding = {
  code: '37111-2',
  display: 'Knee X-ray AP and lateral and Sunrise and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayAPAndLateralAndTunnel: Coding = {
  code: '37112-0',
  display: 'Knee X-ray AP and lateral and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayAPAndLateralAndTunnel: Coding = {
  code: '37113-8',
  display: 'Knee - bilateral X-ray AP and lateral and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayAPAndLateralAndTunnel: Coding = {
  code: '37114-6',
  display: 'Knee - left X-ray AP and lateral and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayAPAndLateralAndObliqueAndTunnel: Coding = {
  code: '37115-3',
  display: 'Knee X-ray AP and lateral and oblique and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayAPAndLateralAndSunriseAndTunnel: Coding = {
  code: '37116-1',
  display: 'Knee - bilateral X-ray AP and lateral and Sunrise and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayAPAndLateralAndSunriseAndTunnel: Coding = {
  code: '37117-9',
  display: 'Knee - left X-ray AP and lateral and Sunrise and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayAPAndLateralAndObliqueAndSunriseAndTunnel: Coding = {
  code: '37118-7',
  display: 'Knee - bilateral X-ray AP and lateral and oblique and Sunrise and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPAndOblique: Coding = {
  code: '37119-5',
  display: 'Abdomen X-ray AP and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPAndOdontoidAndLateralCrosstable: Coding = {
  code: '37120-3',
  display: 'Spine Cervical X-ray AP and odontoid and lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleLeftXRayAPAndSerendipity: Coding = {
  code: '37121-1',
  display: 'Clavicle - left X-ray AP and Serendipity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPAndStrykerNotch: Coding = {
  code: '37122-9',
  display: 'Shoulder - left X-ray AP and Stryker Notch',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPAndWestPoint: Coding = {
  code: '37123-7',
  display: 'Shoulder - left X-ray AP and West Point',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaLeftXRayAPAndY: Coding = {
  code: '37124-5',
  display: 'Scapula - left X-ray AP and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPAndY: Coding = {
  code: '37125-2',
  display: 'Shoulder - left X-ray AP and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAPAndAxillaryAndY: Coding = {
  code: '37126-0',
  display: 'Shoulder - bilateral X-ray AP and axillary and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAxillaryAndY: Coding = {
  code: '37127-8',
  display: 'Shoulder - bilateral X-ray axillary and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAxillaryAndY: Coding = {
  code: '37128-6',
  display: 'Shoulder - left X-ray axillary and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayRightLateralAndLeftLateral: Coding = {
  code: '37131-0',
  display: 'Abdomen X-ray right lateral and left lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayLateralWFlexionAndWExtension: Coding = {
  code: '37132-8',
  display: 'Spine Lumbar X-ray lateral W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayLateralWFlexionAndWExtension: Coding = {
  code: '37133-6',
  display: 'Spine Cervical X-ray lateral W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRayLateralAndMortise: Coding = {
  code: '37134-4',
  display: 'Ankle - bilateral X-ray lateral and Mortise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayLateralAndMortise: Coding = {
  code: '37135-1',
  display: 'Ankle - left X-ray lateral and Mortise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayLateralAndY: Coding = {
  code: '37136-9',
  display: 'Shoulder - left X-ray lateral and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyXRayLimitedWContrastIV: Coding = {
  code: '37137-7',
  display: 'Kidney X-ray limited W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayRightObliqueAndLeftOblique: Coding = {
  code: '37138-5',
  display: 'Abdomen X-ray right oblique and left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayObliqueAndLateralWFlexionAndWExtension: Coding = {
  code: '37139-3',
  display: 'Spine Cervical X-ray oblique and lateral W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayOutletAndY: Coding = {
  code: '37140-1',
  display: 'Shoulder - left X-ray outlet and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndRightLateral: Coding = {
  code: '37141-9',
  display: 'Chest X-ray PA and right lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralXRayPAAndLateralAndBallCatcher: Coding = {
  code: '37142-7',
  display: 'Hand - bilateral X-ray PA and lateral and Ball Catcher',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateralAndAPLateralDecubitus: Coding = {
  code: '37143-5',
  display: 'Chest X-ray PA and lateral and AP lateral-decubitus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateralAndAPLeftLateralDecubitus: Coding = {
  code: '37144-3',
  display: 'Chest X-ray PA and lateral and AP left lateral-decubitus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateralAndAPRightLateralDecubitus: Coding = {
  code: '37145-0',
  display: 'Chest X-ray PA and lateral and AP right lateral-decubitus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateralAndLeftOblique: Coding = {
  code: '37146-8',
  display: 'Chest X-ray PA and lateral and left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateralAndRightOblique: Coding = {
  code: '37147-6',
  display: 'Chest X-ray PA and lateral and right oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleXRayPAAndLateralAndObliqueAndTowne: Coding = {
  code: '37148-4',
  display: 'Mandible X-ray PA and lateral and oblique and Towne',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaLeftXRayPAAndLateralAndSunrise: Coding = {
  code: '37149-2',
  display: 'Patella - left X-ray PA and lateral and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndRightObliqueAndLeftOblique: Coding = {
  code: '37150-0',
  display: 'Chest X-ray PA and right oblique and left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyPortable: Coding = {
  code: '37151-8',
  display: 'Unspecified body region Fluoroscopy portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayOutletAndY: Coding = {
  code: '37152-6',
  display: 'Shoulder - bilateral X-ray outlet and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidXRayStenverAndArcelin: Coding = {
  code: '37153-4',
  display: 'Mastoid X-ray Stenver and Arcelin',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayObliqueAndSunrise: Coding = {
  code: '37154-2',
  display: 'Knee X-ray oblique and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayObliqueAndSunriseAndTunnel: Coding = {
  code: '37155-9',
  display: 'Knee X-ray oblique and Sunrise and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRaySunriseAndTunnel: Coding = {
  code: '37156-7',
  display: 'Knee - left X-ray Sunrise and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayGrasheyAndOutlet: Coding = {
  code: '37157-5',
  display: 'Shoulder - left X-ray Grashey and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayGrasheyAndAxillaryAndOutlet: Coding = {
  code: '37158-3',
  display: 'Shoulder - left X-ray Grashey and axillary and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRayTarsal: Coding = {
  code: '37159-1',
  display: 'Foot - left X-ray tarsal',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayGrasheyAndAxillary: Coding = {
  code: '37160-9',
  display: 'Shoulder - left X-ray Grashey and axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayGrasheyAndAxillaryAndOutletAndZanca: Coding = {
  code: '37161-7',
  display: 'Shoulder - bilateral X-ray Grashey and axillary and outlet and Zanca',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayGrasheyAndOutletAndSerendipity: Coding = {
  code: '37162-5',
  display: 'Shoulder - left X-ray Grashey and outlet and Serendipity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRaySunriseAndTunnel: Coding = {
  code: '37163-3',
  display: 'Knee - bilateral X-ray Sunrise and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesXRayLateralAndCaldwellAndWaters: Coding = {
  code: '37164-1',
  display: 'Facial bones X-ray lateral and Caldwell and Waters',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesXRayLateralAndCaldwellAndWatersAndSubmentovertex: Coding = {
  code: '37165-8',
  display: 'Facial bones X-ray lateral and Caldwell and Waters and submentovertex',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesXRayLateralAndCaldwellAndWatersAndSubmentovertexAndTowne: Coding = {
  code: '37166-6',
  display: 'Facial bones X-ray lateral and Caldwell and Waters and submentovertex and Towne',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayGrasheyAndWestPoint: Coding = {
  code: '37167-4',
  display: 'Shoulder - left X-ray Grashey and West Point',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayPortable: Coding = {
  code: '37168-2',
  display: 'Hip X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayPortable: Coding = {
  code: '37169-0',
  display: 'Hip - left X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusXRayPortable: Coding = {
  code: '37170-8',
  display: 'Humerus X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayPortable: Coding = {
  code: '37171-6',
  display: 'Spine Cervical X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayPortable: Coding = {
  code: '37172-4',
  display: 'Spine Lumbar X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CerebralArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37173-2',
  display: 'Cerebral artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CoronaryArteriesFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37174-0',
  display: 'Coronary arteries Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37175-7',
  display: 'Femoral artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralArteryAndPoplitealArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37176-5',
  display: 'Femoral artery and Popliteal artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacArteryBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37177-3',
  display: 'Iliac artery - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37178-1',
  display: 'Iliac artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InferiorMesentericArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37179-9',
  display: 'Inferior mesenteric artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SuperiorMesentericArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37180-7',
  display: 'Superior mesenteric artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PoplitealArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37181-5',
  display: 'Popliteal artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37182-3',
  display: 'Pulmonary artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleFluoroscopyWContrastIntraarticular: Coding = {
  code: '37183-1',
  display: 'Ankle Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralFluoroscopyWContrastIntraarticular: Coding = {
  code: '37184-9',
  display: 'Ankle - bilateral Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftFluoroscopyWContrastIntraarticular: Coding = {
  code: '37185-6',
  display: 'Ankle - left Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowFluoroscopyWContrastIntraarticular: Coding = {
  code: '37186-4',
  display: 'Elbow Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralFluoroscopyWContrastIntraarticular: Coding = {
  code: '37187-2',
  display: 'Elbow - bilateral Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftFluoroscopyWContrastIntraarticular: Coding = {
  code: '37188-0',
  display: 'Elbow - left Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointBilateralFluoroscopyWContrastIntraarticular: Coding = {
  code: '37189-8',
  display: 'Sacroiliac joint - bilateral Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointLeftFluoroscopyWContrastIntraarticular: Coding = {
  code: '37190-6',
  display: 'Sacroiliac joint - left Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JointFluoroscopyWContrastIntraarticular: Coding = {
  code: '37191-4',
  display: 'Joint Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalFluoroscopyWContrastIntradisc: Coding = {
  code: '37192-2',
  display: 'Spine Cervical Fluoroscopy W contrast intradisc',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarFluoroscopyWContrastIntradisc: Coding = {
  code: '37193-0',
  display: 'Spine Lumbar Fluoroscopy W contrast intradisc',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CerebralVeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37195-5',
  display: 'Cerebral vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinsLeftFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37196-3',
  display: 'Lower extremity veins - left Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JugularVeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37197-1',
  display: 'Jugular vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EsophagusXRayWContrastPO: Coding = {
  code: '37198-9',
  display: 'Esophagus X-ray W contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestFluoroscopyWContrastPO: Coding = {
  code: '37199-7',
  display: 'Chest Fluoroscopy W contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayWContrastPO: Coding = {
  code: '37200-3',
  display: 'Chest X-ray W contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRayStanding: Coding = {
  code: '37201-1',
  display: 'Ankle X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRayStanding: Coding = {
  code: '37202-9',
  display: 'Ankle - bilateral X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayStanding: Coding = {
  code: '37203-7',
  display: 'Ankle - left X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityXRayStanding: Coding = {
  code: '37204-5',
  display: 'Lower extremity X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusXRayStanding: Coding = {
  code: '37205-2',
  display: 'Calcaneus X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusLeftXRayStanding: Coding = {
  code: '37206-0',
  display: 'Calcaneus - left X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayStanding: Coding = {
  code: '37207-8',
  display: 'Hip - left X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayStanding: Coding = {
  code: '37208-6',
  display: 'Spine Lumbar X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesLeftXRayStanding: Coding = {
  code: '37209-4',
  display: 'Toes - left X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationOfCystOfAbdomen: Coding = {
  code: '37210-2',
  display: 'CT Guidance for aspiration of cyst of Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfBoneMarrow: Coding = {
  code: '37211-0',
  display: 'CT Guidance for biopsy of Bone marrow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfEpididymis: Coding = {
  code: '37212-8',
  display: 'CT Guidance for biopsy of Epididymis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfMediastinum: Coding = {
  code: '37213-6',
  display: 'CT Guidance for biopsy of Mediastinum',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfSuperficialTissue: Coding = {
  code: '37214-4',
  display: 'CT Guidance for biopsy of Superficial tissue',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainAndLarynxMRIWContrastIV: Coding = {
  code: '37215-1',
  display: 'Brain and Larynx MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaEndograftCT: Coding = {
  code: '37216-9',
  display: 'Aorta.endograft CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainStemAndNervesCranialMRI: Coding = {
  code: '37217-7',
  display: 'Brain Stem and Nerves.cranial MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainTemporalMRI: Coding = {
  code: '37218-5',
  display: 'Brain.temporal MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BiliaryDuctsMRI: Coding = {
  code: '37219-3',
  display: 'Biliary ducts MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BiliaryDuctsAndPancreaticDuctMRI: Coding = {
  code: '37220-1',
  display: 'Biliary ducts and Pancreatic duct MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FistulaCT: Coding = {
  code: '37221-9',
  display: 'Fistula CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleAndFootMRI: Coding = {
  code: '37222-7',
  display: 'Ankle and Foot MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandCT: Coding = {
  code: '37223-5',
  display: 'Parotid gland CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandMRI: Coding = {
  code: '37224-3',
  display: 'Parotid gland MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointCT: Coding = {
  code: '37225-0',
  display: 'Sternoclavicular Joint CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointCT: Coding = {
  code: '37226-8',
  display: 'Temporomandibular joint CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointBilateralXRayTomograph: Coding = {
  code: '37227-6',
  display: 'Temporomandibular joint - bilateral X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointBilateralMRI: Coding = {
  code: '37228-4',
  display: 'Temporomandibular joint - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointLeftXRayTomograph: Coding = {
  code: '37229-2',
  display: 'Temporomandibular joint - left X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointLeftMRI: Coding = {
  code: '37230-0',
  display: 'Temporomandibular joint - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointRightMRI: Coding = {
  code: '37231-8',
  display: 'Temporomandibular joint - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbosacralJunctionCT: Coding = {
  code: '37232-6',
  display: 'Spine Lumbosacral Junction CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MediastinumXRayTomograph: Coding = {
  code: '37233-4',
  display: 'Mediastinum X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MediastinumMRI: Coding = {
  code: '37234-2',
  display: 'Mediastinum MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CircleOfWillisMRIAngiogram: Coding = {
  code: '37235-9',
  display: 'Circle of Willis MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GreatVesselMRI: Coding = {
  code: '37236-7',
  display: 'Great vessel MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusTractCTWContrastIntraSinusTract: Coding = {
  code: '37237-5',
  display: 'Sinus tract CT W contrast intra sinus tract',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointCTWContrastIntraarticular: Coding = {
  code: '37238-3',
  display: 'Lower Extremity Joint CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainAndInternalAuditoryCanalMRIWContrastIV: Coding = {
  code: '37239-1',
  display: 'Brain and Internal auditory canal MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandCTWContrastIV: Coding = {
  code: '37240-9',
  display: 'Parotid gland CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandMRIWContrastIV: Coding = {
  code: '37241-7',
  display: 'Parotid gland MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointCTWContrastIV: Coding = {
  code: '37242-5',
  display: 'Sternoclavicular Joint CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointCTWContrastIV: Coding = {
  code: '37243-3',
  display: 'Temporomandibular joint CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointMRIWContrastIV: Coding = {
  code: '37244-1',
  display: 'Temporomandibular joint MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointBilateralMRIWContrastIV: Coding = {
  code: '37245-8',
  display: 'Temporomandibular joint - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointLeftCTWContrastIV: Coding = {
  code: '37246-6',
  display: 'Temporomandibular joint - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointLeftMRIWContrastIV: Coding = {
  code: '37247-4',
  display: 'Temporomandibular joint - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointRightCTWContrastIV: Coding = {
  code: '37248-2',
  display: 'Temporomandibular joint - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointRightMRIWContrastIV: Coding = {
  code: '37249-0',
  display: 'Temporomandibular joint - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SoftTissueMRIWContrastIV: Coding = {
  code: '37253-2',
  display: 'Soft tissue MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CircleOfWillisMRIAngiogramWContrastIV: Coding = {
  code: '37254-0',
  display: 'Circle of Willis MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndSpineLumbarXRay3Views: Coding = {
  code: '37256-5',
  display: 'Pelvis and Spine Lumbar X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarAndSacroiliacJointXRay3Views: Coding = {
  code: '37257-3',
  display: 'Spine Lumbar and Sacroiliac Joint X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarAndSacrumXRay3Views: Coding = {
  code: '37259-9',
  display: 'Spine Lumbar and Sacrum X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarAndSacrumAndCoccyxXRay3Views: Coding = {
  code: '37260-7',
  display: 'Spine Lumbar and Sacrum and Coccyx X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarAndSacrumAndSacroiliacJointAndCoccyxXRay3Views: Coding = {
  code: '37261-5',
  display: 'Spine Lumbar and Sacrum and Sacroiliac Joint and Coccyx X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandMRIWAndWOContrastIV: Coding = {
  code: '37265-6',
  display: 'Parotid gland MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointCTWAndWOContrastIV: Coding = {
  code: '37266-4',
  display: 'Sternoclavicular Joint CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointCTWAndWOContrastIV: Coding = {
  code: '37267-2',
  display: 'Temporomandibular joint CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointMRIWAndWOContrastIV: Coding = {
  code: '37268-0',
  display: 'Temporomandibular joint MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointBilateralMRIWAndWOContrastIV: Coding = {
  code: '37269-8',
  display: 'Temporomandibular joint - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointLeftMRIWAndWOContrastIV: Coding = {
  code: '37270-6',
  display: 'Temporomandibular joint - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointRightMRIWAndWOContrastIV: Coding = {
  code: '37271-4',
  display: 'Temporomandibular joint - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MediastinumMRIWAndWOContrastIV: Coding = {
  code: '37272-2',
  display: 'Mediastinum MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpinalVeinMRIAngiogramWAndWOContrastIV: Coding = {
  code: '37277-1',
  display: 'Spinal vein MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainAndInternalAuditoryCanalMRIWOContrast: Coding = {
  code: '37278-9',
  display: 'Brain and Internal auditory canal MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainAndLarynxMRIWOContrast: Coding = {
  code: '37279-7',
  display: 'Brain and Larynx MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandCTWOContrast: Coding = {
  code: '37280-5',
  display: 'Parotid gland CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandMRIWOContrast: Coding = {
  code: '37281-3',
  display: 'Parotid gland MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointCTWOContrast: Coding = {
  code: '37282-1',
  display: 'Sternoclavicular Joint CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointCTWOContrast: Coding = {
  code: '37283-9',
  display: 'Temporomandibular joint CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointMRIWOContrast: Coding = {
  code: '37284-7',
  display: 'Temporomandibular joint MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointBilateralMRIWOContrast: Coding = {
  code: '37285-4',
  display: 'Temporomandibular joint - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointLeftMRIWOContrast: Coding = {
  code: '37286-2',
  display: 'Temporomandibular joint - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointRightMRIWOContrast: Coding = {
  code: '37287-0',
  display: 'Temporomandibular joint - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbosacralJunctionCTWOContrast: Coding = {
  code: '37288-8',
  display: 'Spine Lumbosacral Junction CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SoftTissueMRIWOContrast: Coding = {
  code: '37293-8',
  display: 'Soft tissue MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCTAnd3DReconstruction: Coding = {
  code: '37294-6',
  display: 'Head CT and 3D reconstruction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurAndHipCTAndAnteversionMeasurement: Coding = {
  code: '37295-3',
  display: 'Femur and Hip CT and anteversion measurement',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenAndFetusXRayViewForFetalAge: Coding = {
  code: '37297-9',
  display: 'Abdomen and Fetus X-ray View for fetal age',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointBilateralXRaySerendipity: Coding = {
  code: '37298-7',
  display: 'Sternoclavicular joint - bilateral X-ray Serendipity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointLeftXRaySerendipity: Coding = {
  code: '37299-5',
  display: 'Sternoclavicular joint - left X-ray Serendipity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbosacralJunctionXRayTrueAP: Coding = {
  code: '37300-1',
  display: 'Spine Lumbosacral Junction X-ray true AP',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayScaphoid: Coding = {
  code: '37302-7',
  display: 'Wrist - left X-ray scaphoid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesAndZygomaticArchXRay: Coding = {
  code: '37303-5',
  display: 'Facial bones and Zygomatic arch X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRayScaphoid: Coding = {
  code: '37304-3',
  display: 'Wrist - bilateral X-ray scaphoid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusBicipitalGrooveXRay: Coding = {
  code: '37319-1',
  display: 'Humerus bicipital groove X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusBicipitalGrooveLeftXRay: Coding = {
  code: '37320-9',
  display: 'Humerus bicipital groove - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusBicipitalGrooveBilateralXRay: Coding = {
  code: '37321-7',
  display: 'Humerus bicipital groove - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointBilateralXRay: Coding = {
  code: '37323-3',
  display: 'Sternoclavicular joint - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointLeftXRay: Coding = {
  code: '37324-1',
  display: 'Sternoclavicular joint - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointBilateralXRay: Coding = {
  code: '37325-8',
  display: 'Temporomandibular joint - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OlecranonLeftXRay: Coding = {
  code: '37332-4',
  display: 'Olecranon - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullAndFacialBonesAndMandibleXRay: Coding = {
  code: '37338-1',
  display: 'Skull and Facial bones and Mandible X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarAndSacrumXRay: Coding = {
  code: '37340-7',
  display: 'Spine Lumbar and Sacrum X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarAndSacrumAndCoccyxXRay: Coding = {
  code: '37341-5',
  display: 'Spine Lumbar and Sacrum and Coccyx X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarAndSacrumAndSacroiliacJointAndCoccyxXRay: Coding = {
  code: '37342-3',
  display: 'Spine Lumbar and Sacrum and Sacroiliac Joint and Coccyx X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesBilateralXRay2Views: Coding = {
  code: '37348-0',
  display: 'Toes - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointBilateralXRay5Views: Coding = {
  code: '37350-6',
  display: 'Temporomandibular joint - bilateral X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndSpineLumbarXRay5Views: Coding = {
  code: '37351-4',
  display: 'Pelvis and Spine Lumbar X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarAndSacroiliacJointXRay5Views: Coding = {
  code: '37353-0',
  display: 'Spine Lumbar and Sacroiliac Joint X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarAndSacrumXRay5Views: Coding = {
  code: '37355-5',
  display: 'Spine Lumbar and Sacrum X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarAndSacrumAndCoccyxXRay5Views: Coding = {
  code: '37356-3',
  display: 'Spine Lumbar and Sacrum and Coccyx X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarAndSacrumAndSacroiliacJointAndCoccyxXRay5Views: Coding = {
  code: '37357-1',
  display: 'Spine Lumbar and Sacrum and Sacroiliac Joint and Coccyx X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndSpineThoracicXRayAPAndLateral: Coding = {
  code: '37361-3',
  display: 'Spine Cervical and Spine Thoracic X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BonesXRayBoneAge: Coding = {
  code: '37362-1',
  display: 'Bones X-ray bone age',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAndFemoralArteryLeftFluoroscopicAngiogramRunoffWContrastIA: Coding = {
  code: '37364-7',
  display: 'Aorta and Femoral artery - left Fluoroscopic angiogram runoff W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BonesXRaySurveyForMetastasis: Coding = {
  code: '37365-4',
  display: 'Bones X-ray survey for metastasis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalAortaAndArteriesFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37366-2',
  display: 'Abdominal Aorta and Arteries Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndUpperExtremityArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37379-5',
  display: 'Aortic arch and Upper Extremity artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndBrachialArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37380-3',
  display: 'Aortic arch and Brachial artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndCarotidArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37381-1',
  display: 'Aortic arch and Carotid artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndSubclavianArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37382-9',
  display: 'Aortic arch and Subclavian artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndSubclavianArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37383-7',
  display: 'Aortic arch and Subclavian artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndVertebralArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37384-5',
  display: 'Aortic arch and Vertebral artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndVertebralArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37385-2',
  display: 'Aortic arch and Vertebral artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndVertebralArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37386-0',
  display: 'Aortic arch and Vertebral artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37387-8',
  display: 'Adrenal artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrachialArteryBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37388-6',
  display: 'Brachial artery - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BronchialArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37389-4',
  display: 'Bronchial artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryExternalLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37390-2',
  display: 'Carotid artery.external - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryAndVertebralArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37391-0',
  display: 'Carotid artery and Vertebral artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryAndVertebralArteryBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37392-8',
  display: 'Carotid artery and Vertebral artery - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryPlusVertebralArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37393-6',
  display: 'Carotid artery+Vertebral artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CeliacArteryAndSuperiorMesentericArteryAndInferiorMesentericArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37394-4',
  display: 'Celiac artery and Superior mesenteric artery and Inferior mesenteric artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteriesLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37395-1',
  display: 'Extremity arteries - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteriesBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37396-9',
  display: 'Upper extremity arteries - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastricArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37397-7',
  display: 'Gastric artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastricArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37398-5',
  display: 'Gastric artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastroduodenalArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37399-3',
  display: 'Gastroduodenal artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MaxillaryArteryInternalFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37401-7',
  display: 'Maxillary artery.internal Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SuperiorMesentericArteryAndInferiorMesentericArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37402-5',
  display: 'Superior mesenteric artery and Inferior mesenteric artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CeliacArteryAndGastricArteryLeftAndSuperiorMesentericArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37403-3',
  display: 'Celiac artery and Gastric artery - left and Superior mesenteric artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PudendalArteryInternalFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37404-1',
  display: 'Pudendal artery.internal Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SubclavianArteryBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37405-8',
  display: 'Subclavian artery - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SubclavianArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37406-6',
  display: 'Subclavian artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VertebralArteryBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37407-4',
  display: 'Vertebral artery - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointBilateralFluoroscopyWContrastIntraarticular: Coding = {
  code: '37409-0',
  display: 'Temporomandibular joint - bilateral Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointLeftFluoroscopyWContrastIntraarticular: Coding = {
  code: '37410-8',
  display: 'Temporomandibular joint - left Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AzygosVeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37411-6',
  display: 'Azygos vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinsBilateralFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37412-4',
  display: 'Extremity veins - bilateral Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinsLeftFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37413-2',
  display: 'Extremity veins - left Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinsBilateralFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37414-0',
  display: 'Lower extremity veins - bilateral Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinsBilateralFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37415-7',
  display: 'Upper extremity veins - bilateral Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralVeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37416-5',
  display: 'Femoral vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IntraosseousVeinsFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37419-9',
  display: 'Intraosseous veins Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JugularVeinLeftFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37420-7',
  display: 'Jugular vein - left Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InferiorMesentericVeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37421-5',
  display: 'Inferior mesenteric vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitVeinsLeftFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37422-3',
  display: 'Orbit veins - left Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVeinLeftFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37423-1',
  display: 'Renal vein - left Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinFluoroscopicAngiogramAngioplastyWContrastIV: Coding = {
  code: '37426-4',
  display: 'Lower extremity vein Fluoroscopic angiogram Angioplasty W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpine: Coding = {
  code: '37427-2',
  display: 'Fluoroscopy Guidance for injection of Spine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristCT: Coding = {
  code: '37428-0',
  display: 'Wrist CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRayTomograph: Coding = {
  code: '37429-8',
  display: 'Wrist - bilateral X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralCT: Coding = {
  code: '37430-6',
  display: 'Wrist - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftCT: Coding = {
  code: '37431-4',
  display: 'Wrist - left CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayTomograph: Coding = {
  code: '37432-2',
  display: 'Wrist - left X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightCT: Coding = {
  code: '37433-0',
  display: 'Wrist - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartMRICineForFunction: Coding = {
  code: '37434-8',
  display: 'Heart MRI cine for function',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointMRICine: Coding = {
  code: '37435-5',
  display: 'Temporomandibular joint MRI cine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainMRIDiffusionWeighted: Coding = {
  code: '37436-3',
  display: 'Brain MRI diffusion weighted',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMRIDynamicWContrastIV: Coding = {
  code: '37437-1',
  display: 'Breast MRI dynamic W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PituitaryAndSellaTurcicaCTDynamicWContrastIV: Coding = {
  code: '37438-9',
  display: 'Pituitary and Sella turcica CT dynamic W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestCTHighResolution: Coding = {
  code: '37439-7',
  display: 'Chest CT high resolution',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestCTHighResolutionWContrastIV: Coding = {
  code: '37440-5',
  display: 'Chest CT high resolution W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestCTHighResolutionWOContrast: Coding = {
  code: '37441-3',
  display: 'Chest CT high resolution WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainMRISpectroscopy: Coding = {
  code: '37442-1',
  display: 'Brain MRI spectroscopy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionMRISpectroscopy: Coding = {
  code: '37443-9',
  display: 'Unspecified body region MRI spectroscopy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristMRIWContrastIntraarticular: Coding = {
  code: '37444-7',
  display: 'Wrist MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftMRIWContrastIntraarticular: Coding = {
  code: '37445-4',
  display: 'Wrist - left MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightMRIWContrastIntraarticular: Coding = {
  code: '37446-2',
  display: 'Wrist - right MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristCTWContrastIV: Coding = {
  code: '37447-0',
  display: 'Wrist CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristMRIWContrastIV: Coding = {
  code: '37448-8',
  display: 'Wrist MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralMRIWContrastIV: Coding = {
  code: '37449-6',
  display: 'Wrist - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftCTWContrastIV: Coding = {
  code: '37450-4',
  display: 'Wrist - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftMRIWContrastIV: Coding = {
  code: '37451-2',
  display: 'Wrist - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightCTWContrastIV: Coding = {
  code: '37452-0',
  display: 'Wrist - right CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightMRIWContrastIV: Coding = {
  code: '37453-8',
  display: 'Wrist - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRay3Views: Coding = {
  code: '37454-6',
  display: 'Wrist - bilateral X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRay3Views: Coding = {
  code: '37455-3',
  display: 'Wrist - left X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristCTWAndWOContrastIV: Coding = {
  code: '37457-9',
  display: 'Wrist CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftCTWAndWOContrastIV: Coding = {
  code: '37458-7',
  display: 'Wrist - left CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristCTWOContrast: Coding = {
  code: '37459-5',
  display: 'Wrist CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristMRIWOContrast: Coding = {
  code: '37460-3',
  display: 'Wrist MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralCTWOContrast: Coding = {
  code: '37461-1',
  display: 'Wrist - bilateral CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralMRIWOContrast: Coding = {
  code: '37462-9',
  display: 'Wrist - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftCTWOContrast: Coding = {
  code: '37463-7',
  display: 'Wrist - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftMRIWOContrast: Coding = {
  code: '37464-5',
  display: 'Wrist - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightCTWOContrast: Coding = {
  code: '37465-2',
  display: 'Wrist - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightMRIWOContrast: Coding = {
  code: '37466-0',
  display: 'Wrist - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointXRay10DegreeCephalicAngle: Coding = {
  code: '37467-8',
  display: 'Acromioclavicular Joint X-ray 10 degree cephalic angle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRay30DegreeCaudalAngle: Coding = {
  code: '37468-6',
  display: 'Shoulder - bilateral X-ray 30 degree caudal angle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleBilateralXRay45DegreeCephalicAngle: Coding = {
  code: '37469-4',
  display: 'Clavicle - bilateral X-ray 45 degree cephalic angle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleLeftXRay45DegreeCephalicAngle: Coding = {
  code: '37470-2',
  display: 'Clavicle - left X-ray 45 degree cephalic angle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralXRayBora: Coding = {
  code: '37471-0',
  display: 'Hand - bilateral X-ray Bora',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRayBora: Coding = {
  code: '37472-8',
  display: 'Hand - left X-ray Bora',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayGrashey: Coding = {
  code: '37473-6',
  display: 'Shoulder - left X-ray Grashey',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayLateralWManualStress: Coding = {
  code: '37474-4',
  display: 'Ankle - left X-ray lateral W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayMortiseWManualStress: Coding = {
  code: '37475-1',
  display: 'Ankle - left X-ray Mortise W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayPAW45DegreeFlexion: Coding = {
  code: '37476-9',
  display: 'Knee X-ray PA W 45 degree flexion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayPAStandingAndW45DegreeFlexion: Coding = {
  code: '37477-7',
  display: 'Knee X-ray PA standing and W 45 degree flexion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndSpineThoracicXRay: Coding = {
  code: '37481-9',
  display: 'Spine Cervical and Spine Thoracic X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRay2Views: Coding = {
  code: '37482-7',
  display: 'Wrist - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRay2Views: Coding = {
  code: '37483-5',
  display: 'Wrist - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayAPWManualStress: Coding = {
  code: '37484-3',
  display: 'Knee - left X-ray AP W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusXRayAPAndTransthoracic: Coding = {
  code: '37485-0',
  display: 'Humerus X-ray AP and transthoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRayBrodenWManualStress: Coding = {
  code: '37486-8',
  display: 'Ankle X-ray Broden W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityArteriesFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37487-6',
  display: 'Lower extremity arteries Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteriesLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37488-4',
  display: 'Upper extremity arteries - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibioperonealArteriesFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37489-2',
  display: 'Tibioperoneal arteries Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VertebralArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37490-0',
  display: 'Vertebral artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationOfPleuralSpace: Coding = {
  code: '37491-8',
  display: 'CT Guidance for aspiration of Pleural space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfChestPleura: Coding = {
  code: '37492-6',
  display: 'CT Guidance for biopsy of Chest.pleura',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForInjectionOfSpineDiscCervical: Coding = {
  code: '37493-4',
  display: 'CT Guidance for injection of Spine.disc.cervical',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfTendon: Coding = {
  code: '37494-2',
  display: 'Fluoroscopy Guidance for injection of Tendon',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullBaseCT: Coding = {
  code: '37495-9',
  display: 'Skull.base CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalCTWContrastIntradisc: Coding = {
  code: '37496-7',
  display: 'Spine Cervical CT W contrast intradisc',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineVesselsMRIAngiogram: Coding = {
  code: '37497-5',
  display: 'Spine vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVesselsAndNeckVesselsCTAngiogramWContrastIV: Coding = {
  code: '37498-3',
  display: 'Head vessels and Neck vessels CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticStentCTAngiogramWContrastIV: Coding = {
  code: '37499-1',
  display: 'Aortic stent CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineVesselsMRIAngiogramWContrastIV: Coding = {
  code: '37500-6',
  display: 'Spine vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CervicalSpineVesselsMRIAngiogramWContrastIV: Coding = {
  code: '37501-4',
  display: 'Cervical Spine vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LumbarSpineVesselsMRIAngiogramWContrastIV: Coding = {
  code: '37502-2',
  display: 'Lumbar Spine vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSpineVesselsMRIAngiogramWContrastIV: Coding = {
  code: '37503-0',
  display: 'Thoracic Spine vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '37505-5',
  display: 'Spine vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CervicalSpineVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '37506-3',
  display: 'Cervical Spine vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LumbarSpineVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '37507-1',
  display: 'Lumbar Spine vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSpineVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '37508-9',
  display: 'Thoracic Spine vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarCTWContrastIntradisc: Coding = {
  code: '37509-7',
  display: 'Spine Lumbar CT W contrast intradisc',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineVesselsMRIAngiogramWOContrast: Coding = {
  code: '37510-5',
  display: 'Spine vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CervicalSpineVesselsMRIAngiogramWOContrast: Coding = {
  code: '37511-3',
  display: 'Cervical Spine vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSpineVesselsMRIAngiogramWOContrast: Coding = {
  code: '37512-1',
  display: 'Thoracic Spine vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaBilateralXRay10DegreeCaudalAngle: Coding = {
  code: '37513-9',
  display: 'Tibia - bilateral X-ray 10 degree caudal angle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaLeftXRay10DegreeCaudalAngle: Coding = {
  code: '37514-7',
  display: 'Tibia - left X-ray 10 degree caudal angle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbosacralJunctionXRayLateralSpot: Coding = {
  code: '37515-4',
  display: 'Spine Lumbosacral Junction X-ray lateral spot',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbosacralJunctionXRayLateralSpotStanding: Coding = {
  code: '37516-2',
  display: 'Spine Lumbosacral Junction X-ray lateral spot standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFifthBilateralXRay: Coding = {
  code: '37517-0',
  display: 'Finger fifth - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFifthLeftXRay: Coding = {
  code: '37518-8',
  display: 'Finger fifth - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFourthBilateralXRay: Coding = {
  code: '37519-6',
  display: 'Finger fourth - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFourthLeftXRay: Coding = {
  code: '37520-4',
  display: 'Finger fourth - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerSecondBilateralXRay: Coding = {
  code: '37521-2',
  display: 'Finger second - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerSecondLeftXRay: Coding = {
  code: '37522-0',
  display: 'Finger second - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerThirdBilateralXRay: Coding = {
  code: '37523-8',
  display: 'Finger third - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerThirdLeftXRay: Coding = {
  code: '37524-6',
  display: 'Finger third - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToeFifthLeftXRay: Coding = {
  code: '37530-3',
  display: 'Toe fifth - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToeFourthLeftXRay: Coding = {
  code: '37531-1',
  display: 'Toe fourth - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GreatToeBilateralXRay: Coding = {
  code: '37532-9',
  display: 'Great toe - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GreatToeLeftXRay: Coding = {
  code: '37533-7',
  display: 'Great toe - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToeSecondLeftXRay: Coding = {
  code: '37534-5',
  display: 'Toe second - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToeThirdLeftXRay: Coding = {
  code: '37535-2',
  display: 'Toe third - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayGrasheyAndAxillaryAndY: Coding = {
  code: '37538-6',
  display: 'Shoulder - left X-ray Grashey and axillary and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogramGrid: Coding = {
  code: '37539-4',
  display: 'Breast Mammogram grid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayHolmbladStanding: Coding = {
  code: '37540-2',
  display: 'Knee - bilateral X-ray Holmblad standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidBilateralXRayLawAndMayerAndStenverAndTowne: Coding = {
  code: '37541-0',
  display: 'Mastoid - bilateral X-ray law and Mayer and Stenver and Towne',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogramMagnification: Coding = {
  code: '37542-8',
  display: 'Breast Mammogram magnification',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramMagnification: Coding = {
  code: '37543-6',
  display: 'Breast - bilateral Mammogram magnification',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRayOblique: Coding = {
  code: '37544-4',
  display: 'Wrist - bilateral X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayObliqueCrosstable: Coding = {
  code: '37545-1',
  display: 'Hip - left X-ray oblique crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointBilateralXRayOpenAndClosedMouth: Coding = {
  code: '37546-9',
  display: 'Temporomandibular joint - bilateral X-ray open and closed mouth',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRayPAAndLateral: Coding = {
  code: '37547-7',
  display: 'Wrist - bilateral X-ray PA and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayPAAndLateral: Coding = {
  code: '37548-5',
  display: 'Wrist - left X-ray PA and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRayPAAndLateralAndOblique: Coding = {
  code: '37549-3',
  display: 'Wrist - bilateral X-ray PA and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayPAAndLateralAndOblique: Coding = {
  code: '37550-1',
  display: 'Wrist - left X-ray PA and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogramSpot: Coding = {
  code: '37551-9',
  display: 'Breast Mammogram spot',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramSpot: Coding = {
  code: '37552-7',
  display: 'Breast - bilateral Mammogram spot',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramSpotCompression: Coding = {
  code: '37553-5',
  display: 'Breast - left Mammogram spot compression',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramMagnificationAndSpot: Coding = {
  code: '37554-3',
  display: 'Breast - bilateral Mammogram magnification and spot',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayUlnarDeviationAndRadialDeviation: Coding = {
  code: '37555-0',
  display: 'Wrist - left X-ray ulnar deviation and radial deviation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRayWManualStress: Coding = {
  code: '37556-8',
  display: 'Ankle X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRayWManualStress: Coding = {
  code: '37557-6',
  display: 'Ankle - bilateral X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayWManualStress: Coding = {
  code: '37558-4',
  display: 'Ankle - left X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRayWManualStress: Coding = {
  code: '37559-2',
  display: 'Foot - left X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayWManualStress: Coding = {
  code: '37560-0',
  display: 'Knee X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayWManualStress: Coding = {
  code: '37561-8',
  display: 'Knee - bilateral X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayWManualStress: Coding = {
  code: '37562-6',
  display: 'Knee - left X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbBilateralXRayWManualStress: Coding = {
  code: '37563-4',
  display: 'Thumb - bilateral X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbLeftXRayWManualStress: Coding = {
  code: '37564-2',
  display: 'Thumb - left X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyWBariumContrastViaFistula: Coding = {
  code: '37565-9',
  display: 'Unspecified body region Fluoroscopy W barium contrast via fistula',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyWContrastViaCatheter: Coding = {
  code: '37566-7',
  display: 'Unspecified body region Fluoroscopy W contrast via catheter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColonFluoroscopyWContrastViaColostomy: Coding = {
  code: '37567-5',
  display: 'Colon Fluoroscopy W contrast via colostomy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyWContrastViaFistula: Coding = {
  code: '37568-3',
  display: 'Unspecified body region Fluoroscopy W contrast via fistula',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrinaryBladderFluoroscopyWContrastViaSuprapubicTube: Coding = {
  code: '37569-1',
  display: 'Urinary bladder Fluoroscopy W contrast via suprapubic tube',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralFluoroscopyWContrastIntraarticular: Coding = {
  code: '37570-9',
  display: 'Wrist - bilateral Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftFluoroscopyWContrastIntraarticular: Coding = {
  code: '37571-7',
  display: 'Wrist - left Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineFluoroscopyWContrastIT: Coding = {
  code: '37572-5',
  display: 'Spine Fluoroscopy W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37574-1',
  display: 'Lower extremity vessels Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GallbladderXRayWContrastAndFattyMealPO: Coding = {
  code: '37575-8',
  display: 'Gallbladder X-ray W contrast and fatty meal PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyWGastrografinViaFistula: Coding = {
  code: '37576-6',
  display: 'Unspecified body region Fluoroscopy W gastrografin via fistula',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointXRayWWeight: Coding = {
  code: '37577-4',
  display: 'Acromioclavicular Joint X-ray W weight',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointBilateralXRayWWeight: Coding = {
  code: '37578-2',
  display: 'Acromioclavicular joint - bilateral X-ray W weight',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointXRayWAndWOWeight: Coding = {
  code: '37579-0',
  display: 'Acromioclavicular Joint X-ray W and WO weight',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointBilateralXRayWAndWOWeight: Coding = {
  code: '37580-8',
  display: 'Acromioclavicular joint - bilateral X-ray W and WO weight',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointLeftXRayWAndWOWeight: Coding = {
  code: '37581-6',
  display: 'Acromioclavicular joint - left X-ray W and WO weight',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointXRayWOWeight: Coding = {
  code: '37582-4',
  display: 'Acromioclavicular Joint X-ray WO weight',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipBilateralXRayAndLateralFrog: Coding = {
  code: '37583-2',
  display: 'Pelvis and Hip - bilateral X-ray and lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GreatToeLeftXRayStanding: Coding = {
  code: '37584-0',
  display: 'Great toe - left X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JejunumFluoroscopyWContrast: Coding = {
  code: '37585-7',
  display: 'Jejunum Fluoroscopy W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PenisFluoroscopyWContrastIntraCorpusCavernosum: Coding = {
  code: '37586-5',
  display: 'Penis Fluoroscopy W contrast intra corpus cavernosum',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndCarotidArteryBilateralAndVertebralArteryBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37587-3',
  display: 'Aortic arch and Carotid artery - bilateral and Vertebral artery - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndCarotidArteryCommonBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37588-1',
  display: 'Aortic arch and Carotid artery.common - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndCarotidArteryCommonLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37589-9',
  display: 'Aortic arch and Carotid artery.common - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndCarotidArteryCommonRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37590-7',
  display: 'Aortic arch and Carotid artery.common - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndCarotidArteryExternalBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37591-5',
  display: 'Aortic arch and Carotid artery.external - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndCarotidArteryExternalLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37592-3',
  display: 'Aortic arch and Carotid artery.external - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndCarotidArteryExternalRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37593-1',
  display: 'Aortic arch and Carotid artery.external - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndCarotidArteryAndVertebralArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37594-9',
  display: 'Aortic arch and Carotid artery and Vertebral artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CoronaryGraftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37595-6',
  display: 'Coronary graft Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LymphaticsAbdominalAndLymphaticsPelvicLeftFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '37596-4',
  display: 'Lymphatics abdominal and Lymphatics pelvic - left Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LymphaticsAbdominalAndLymphaticsPelvicFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '37597-2',
  display: 'Lymphatics abdominal and Lymphatics pelvic Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LymphaticsAbdominalAndLymphaticsPelvicBilateralFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '37598-0',
  display: 'Lymphatics abdominal and Lymphatics pelvic - bilateral Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityLymphaticsLeftFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '37599-8',
  display: 'Extremity lymphatics - left Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LymphaticsLeftFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '37600-4',
  display: 'Lymphatics - left Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LymphaticsPelvicBilateralFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '37601-2',
  display: 'Lymphatics pelvic - bilateral Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalVeinLeftFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37602-0',
  display: 'Adrenal vein left Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLeftAndChestXRayLateralAndPAChest: Coding = {
  code: '37603-8',
  display: 'Ribs - left and Chest X-ray lateral and PA chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasalBonesXRay3Views: Coding = {
  code: '37604-6',
  display: 'Nasal bones X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasalBonesXRayLateralAndWaters: Coding = {
  code: '37605-3',
  display: 'Nasal bones X-ray lateral and Waters',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasalBonesXRayTomograph: Coding = {
  code: '37606-1',
  display: 'Nasal bones X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyXRayWContrastIV: Coding = {
  code: '37607-9',
  display: 'Kidney X-ray W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForLocalizationOfForeignBodyOfEye: Coding = {
  code: '37608-7',
  display: 'US Guidance for localization of foreign body of Eye',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OpticForamenXRay4Views: Coding = {
  code: '37609-5',
  display: 'Optic foramen X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitBilateralXRayTomograph: Coding = {
  code: '37611-1',
  display: 'Orbit - bilateral X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitBilateralXRay4Views: Coding = {
  code: '37612-9',
  display: 'Orbit - bilateral X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitBilateralXRayWaters: Coding = {
  code: '37613-7',
  display: 'Orbit - bilateral X-ray Waters',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaXRaySingleView: Coding = {
  code: '37614-5',
  display: 'Patella X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisVesselsFluoroscopicAngiogramWContrast: Coding = {
  code: '37615-2',
  display: 'Pelvis vessels Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRaySingleView: Coding = {
  code: '37616-0',
  display: 'Pelvis X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRay2Views: Coding = {
  code: '37617-8',
  display: 'Pelvis X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayAPAndInlet: Coding = {
  code: '37618-6',
  display: 'Pelvis X-ray AP and inlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayAPAndJudet: Coding = {
  code: '37619-4',
  display: 'Pelvis X-ray AP and Judet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayAPAndLateral: Coding = {
  code: '37620-2',
  display: 'Pelvis X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayAPAndOblique: Coding = {
  code: '37621-0',
  display: 'Pelvis X-ray AP and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayAPSingleView: Coding = {
  code: '37622-8',
  display: 'Pelvis X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayAPAndInletAndOutlet: Coding = {
  code: '37623-6',
  display: 'Pelvis X-ray AP and inlet and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayAPAndLateralAndOblique: Coding = {
  code: '37624-4',
  display: 'Pelvis X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayFerguson: Coding = {
  code: '37625-1',
  display: 'Pelvis X-ray Ferguson',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayLateralFrog: Coding = {
  code: '37626-9',
  display: 'Pelvis X-ray lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayInletAndOutletAndOblique: Coding = {
  code: '37627-7',
  display: 'Pelvis X-ray inlet and outlet and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayInlet: Coding = {
  code: '37628-5',
  display: 'Pelvis X-ray inlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayLateral: Coding = {
  code: '37629-3',
  display: 'Pelvis X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayOblique: Coding = {
  code: '37630-1',
  display: 'Pelvis X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayOutlet: Coding = {
  code: '37631-9',
  display: 'Pelvis X-ray outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayTomograph: Coding = {
  code: '37632-7',
  display: 'Pelvis X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayStanding: Coding = {
  code: '37633-5',
  display: 'Pelvis X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayAP20DegreeCephalicAngle: Coding = {
  code: '37634-3',
  display: 'Pelvis X-ray AP 20 degree cephalic angle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcetabulumXRay3Views: Coding = {
  code: '37635-0',
  display: 'Acetabulum X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRay: Coding = {
  code: '37636-8',
  display: 'Abdomen X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityXRay: Coding = {
  code: '37637-6',
  display: 'Extremity X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckXRay: Coding = {
  code: '37639-2',
  display: 'Neck X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsFluoroscopicAngiogramWContrast: Coding = {
  code: '37640-0',
  display: 'Renal vessels Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightFluoroscopyWContrastIntraarticular: Coding = {
  code: '37641-8',
  display: 'Wrist - right Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayLimited: Coding = {
  code: '37642-6',
  display: 'Wrist - right X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayOblique: Coding = {
  code: '37643-4',
  display: 'Wrist - right X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayTomograph: Coding = {
  code: '37644-2',
  display: 'Wrist - right X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayUlnarDeviation: Coding = {
  code: '37645-9',
  display: 'Wrist - right X-ray ulnar deviation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointXRayLimited: Coding = {
  code: '37646-7',
  display: 'Sacroiliac Joint X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointFluoroscopyWContrastIntraarticular: Coding = {
  code: '37647-5',
  display: 'Sacroiliac Joint Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointXRay3Views: Coding = {
  code: '37648-3',
  display: 'Sacroiliac Joint X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointXRayAPAndOblique: Coding = {
  code: '37649-1',
  display: 'Sacroiliac Joint X-ray AP and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointXRayFerguson: Coding = {
  code: '37650-9',
  display: 'Sacroiliac Joint X-ray Ferguson',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumXRay2Views: Coding = {
  code: '37651-7',
  display: 'Sacrum X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumXRayAPAndLateral: Coding = {
  code: '37652-5',
  display: 'Sacrum X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumXRayTomograph: Coding = {
  code: '37653-3',
  display: 'Sacrum X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaXRaySingleView: Coding = {
  code: '37654-1',
  display: 'Scapula X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaXRay2Views: Coding = {
  code: '37655-8',
  display: 'Scapula X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaXRayY: Coding = {
  code: '37656-6',
  display: 'Scapula X-ray Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRay2ViewsScoliosis: Coding = {
  code: '37658-2',
  display: 'Spine Thoracic and Lumbar X-ray 2 views scoliosis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPStanding: Coding = {
  code: '37659-0',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis AP standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisLateralStanding: Coding = {
  code: '37660-8',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointRightXRay2Views: Coding = {
  code: '37661-6',
  display: 'Acromioclavicular joint - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointRightXRayAPSingleView: Coding = {
  code: '37662-4',
  display: 'Acromioclavicular joint - right X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointRightXRayWAndWOWeight: Coding = {
  code: '37663-2',
  display: 'Acromioclavicular joint - right X-ray W and WO weight',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcetabulumRightXRay2Views: Coding = {
  code: '37664-0',
  display: 'Acetabulum - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRay3Views: Coding = {
  code: '37665-7',
  display: 'Ankle - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayAPAndLateralAndMortise: Coding = {
  code: '37666-5',
  display: 'Ankle - right X-ray AP and lateral and Mortise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayAPAndLateral: Coding = {
  code: '37667-3',
  display: 'Ankle - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayAPAndLateralAndOblique: Coding = {
  code: '37668-1',
  display: 'Ankle - right X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayLateralWManualStress: Coding = {
  code: '37669-9',
  display: 'Ankle - right X-ray lateral W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayLateralAndMortise: Coding = {
  code: '37670-7',
  display: 'Ankle - right X-ray lateral and Mortise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayMortiseWManualStress: Coding = {
  code: '37671-5',
  display: 'Ankle - right X-ray Mortise W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRay2ViewsWManualStress: Coding = {
  code: '37672-3',
  display: 'Ankle - right X-ray 2 views W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayWManualStress: Coding = {
  code: '37673-1',
  display: 'Ankle - right X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayTomograph: Coding = {
  code: '37674-9',
  display: 'Ankle - right X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRay2ViewsStanding: Coding = {
  code: '37675-6',
  display: 'Ankle - right X-ray 2 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayStanding: Coding = {
  code: '37676-4',
  display: 'Ankle - right X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayTunnelCarpal: Coding = {
  code: '37677-2',
  display: 'Wrist - right X-ray tunnel.carpal',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRay2ViewsTunnelCarpal: Coding = {
  code: '37678-0',
  display: 'Wrist - right X-ray 2 views tunnel.carpal',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleRightXRay2Views: Coding = {
  code: '37679-8',
  display: 'Clavicle - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleRightXRayAPAndSerendipity: Coding = {
  code: '37680-6',
  display: 'Clavicle - right X-ray AP and Serendipity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRay2Views: Coding = {
  code: '37681-4',
  display: 'Elbow - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRay3Views: Coding = {
  code: '37682-2',
  display: 'Elbow - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRay4Views: Coding = {
  code: '37683-0',
  display: 'Elbow - right X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRayAPAndLateral: Coding = {
  code: '37684-8',
  display: 'Elbow - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRayAPAndLateralAndOblique: Coding = {
  code: '37685-5',
  display: 'Elbow - right X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRay2ViewsOblique: Coding = {
  code: '37686-3',
  display: 'Elbow - right X-ray 2 views Oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRayOblique: Coding = {
  code: '37687-1',
  display: 'Elbow - right X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRayTomograph: Coding = {
  code: '37688-9',
  display: 'Elbow - right X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightXRaySingleView: Coding = {
  code: '37689-7',
  display: 'Femur - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightXRay2Views: Coding = {
  code: '37690-5',
  display: 'Femur - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightXRay4Views: Coding = {
  code: '37691-3',
  display: 'Femur - right X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightXRayAPAndLateral: Coding = {
  code: '37692-1',
  display: 'Femur - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightXRayStanding: Coding = {
  code: '37693-9',
  display: 'Femur - right X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerRightXRay2Views: Coding = {
  code: '37694-7',
  display: 'Finger - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerRightXRay3Views: Coding = {
  code: '37695-4',
  display: 'Finger - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerRightXRayAPAndLateralAndOblique: Coding = {
  code: '37696-2',
  display: 'Finger - right X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRay2Views: Coding = {
  code: '37697-0',
  display: 'Foot - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRay2ViewsStanding: Coding = {
  code: '37698-8',
  display: 'Foot - right X-ray 2 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRay3Views: Coding = {
  code: '37699-6',
  display: 'Foot - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRay3ViewsStanding: Coding = {
  code: '37700-2',
  display: 'Foot - right X-ray 3 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRayAPAndLateral: Coding = {
  code: '37701-0',
  display: 'Foot - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRayAPAndLateralAndOblique: Coding = {
  code: '37702-8',
  display: 'Foot - right X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRayLateral: Coding = {
  code: '37703-6',
  display: 'Foot - right X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRayObliqueSingleView: Coding = {
  code: '37704-4',
  display: 'Foot - right X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRayWManualStress: Coding = {
  code: '37705-1',
  display: 'Foot - right X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRayTomograph: Coding = {
  code: '37706-9',
  display: 'Foot - right X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusRightAndUlnaRightXRay2Views: Coding = {
  code: '37707-7',
  display: 'Radius - right and Ulna - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusRightAndUlnaRightXRayAPAndLateral: Coding = {
  code: '37708-5',
  display: 'Radius - right and Ulna - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusRightAndUlnaRightXRayOblique: Coding = {
  code: '37709-3',
  display: 'Radius - right and Ulna - right X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRayAPAndLateral: Coding = {
  code: '37710-1',
  display: 'Hand - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRayAPAndLateralAndOblique: Coding = {
  code: '37711-9',
  display: 'Hand - right X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRayLateral: Coding = {
  code: '37712-7',
  display: 'Hand - right X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRayPAAndLateral: Coding = {
  code: '37713-5',
  display: 'Hand - right X-ray PA and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRayPA: Coding = {
  code: '37714-3',
  display: 'Hand - right X-ray PA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRayPAAndLateralAndOblique: Coding = {
  code: '37715-0',
  display: 'Hand - right X-ray PA and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRay: Coding = {
  code: '37716-8',
  display: 'Hand - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRayTomograph: Coding = {
  code: '37717-6',
  display: 'Hand - right X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusRightXRay2Views: Coding = {
  code: '37718-4',
  display: 'Calcaneus - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusRightXRayAPAndLateral: Coding = {
  code: '37719-2',
  display: 'Calcaneus - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusRightXRayStanding: Coding = {
  code: '37720-0',
  display: 'Calcaneus - right X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRay2Views: Coding = {
  code: '37721-8',
  display: 'Hip - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRay3Views: Coding = {
  code: '37722-6',
  display: 'Hip - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayAPAndLateralCrosstable: Coding = {
  code: '37723-4',
  display: 'Hip - right X-ray AP and lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayAPAndLateralFrog: Coding = {
  code: '37724-2',
  display: 'Hip - right X-ray AP and lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayAPAndLateral: Coding = {
  code: '37725-9',
  display: 'Hip - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayAPSingleView: Coding = {
  code: '37726-7',
  display: 'Hip - right X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayLateralCrosstable: Coding = {
  code: '37727-5',
  display: 'Hip - right X-ray lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayObliqueCrosstable: Coding = {
  code: '37728-3',
  display: 'Hip - right X-ray oblique crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayLateralFrog: Coding = {
  code: '37729-1',
  display: 'Hip - right X-ray lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayLateral: Coding = {
  code: '37730-9',
  display: 'Hip - right X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayStanding: Coding = {
  code: '37731-7',
  display: 'Hip - right X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayJudet: Coding = {
  code: '37732-5',
  display: 'Hip - right X-ray Judet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightXRayAPSingleViewStanding: Coding = {
  code: '37733-3',
  display: 'Lower extremity - right X-ray AP single view standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightXRayStanding: Coding = {
  code: '37734-1',
  display: 'Lower extremity - right X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayTomograph: Coding = {
  code: '37735-8',
  display: 'Hip - right X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusRightXRayAPAndLateral: Coding = {
  code: '37736-6',
  display: 'Humerus - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusRightXRayOblique: Coding = {
  code: '37737-4',
  display: 'Humerus - right X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusRightXRay: Coding = {
  code: '37738-2',
  display: 'Humerus - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37739-0',
  display: 'Iliac artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAPAndLateralAndSunriseAndTunnel: Coding = {
  code: '37740-8',
  display: 'Knee - right X-ray AP and lateral and Sunrise and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRaySingleView: Coding = {
  code: '37741-6',
  display: 'Knee - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay3Views: Coding = {
  code: '37742-4',
  display: 'Knee - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay4Views: Coding = {
  code: '37743-2',
  display: 'Knee - right X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay5Views: Coding = {
  code: '37744-0',
  display: 'Knee - right X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAPAndLateral: Coding = {
  code: '37745-7',
  display: 'Knee - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAPWManualStress: Coding = {
  code: '37746-5',
  display: 'Knee - right X-ray AP W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAPAndLateralAndTunnel: Coding = {
  code: '37747-3',
  display: 'Knee - right X-ray AP and lateral and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAPAndLateralAndOblique: Coding = {
  code: '37748-1',
  display: 'Knee - right X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAPAndLateralAndSunrise: Coding = {
  code: '37749-9',
  display: 'Knee - right X-ray AP and lateral and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayLateralHyperextension: Coding = {
  code: '37750-7',
  display: 'Knee - right X-ray lateral hyperextension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayLateral: Coding = {
  code: '37751-5',
  display: 'Knee - right X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayRosenbergStanding: Coding = {
  code: '37752-3',
  display: 'Knee - right X-ray Rosenberg standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayWManualStress: Coding = {
  code: '37753-1',
  display: 'Knee - right X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayLateralStanding: Coding = {
  code: '37754-9',
  display: 'Knee - right X-ray lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayPAStanding: Coding = {
  code: '37755-6',
  display: 'Knee - right X-ray PA standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayTunnelStanding: Coding = {
  code: '37756-4',
  display: 'Knee - right X-ray tunnel standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayOblique: Coding = {
  code: '37757-2',
  display: 'Knee - right X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay: Coding = {
  code: '37758-0',
  display: 'Knee - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRaySunriseAndTunnel: Coding = {
  code: '37759-8',
  display: 'Knee - right X-ray Sunrise and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayTomograph: Coding = {
  code: '37760-6',
  display: 'Knee - right X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayTunnel: Coding = {
  code: '37761-4',
  display: 'Knee - right X-ray tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay2ViewsStanding: Coding = {
  code: '37762-2',
  display: 'Knee - right X-ray 2 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay4ViewsStanding: Coding = {
  code: '37763-0',
  display: 'Knee - right X-ray 4 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightXRaySingleView: Coding = {
  code: '37764-8',
  display: 'Lower extremity - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsRightFluoroscopicAngiogramWContrast: Coding = {
  code: '37765-5',
  display: 'Lower extremity vessels - right Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightXRayTomograph: Coding = {
  code: '37766-3',
  display: 'Lower extremity - right X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinsRightFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37767-1',
  display: 'Lower extremity veins - right Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogram2Views: Coding = {
  code: '37768-9',
  display: 'Breast - right Mammogram 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogramMagnificationAndSpot: Coding = {
  code: '37769-7',
  display: 'Breast - right Mammogram magnification and spot',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogramTangential: Coding = {
  code: '37770-5',
  display: 'Breast - right Mammogram tangential',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogramTrueLateral: Coding = {
  code: '37771-3',
  display: 'Breast - right Mammogram true lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogramXCCL: Coding = {
  code: '37772-1',
  display: 'Breast - right Mammogram XCCL',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogramMagnification: Coding = {
  code: '37773-9',
  display: 'Breast - right Mammogram magnification',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogram: Coding = {
  code: '37774-7',
  display: 'Breast - right Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogramRoll: Coding = {
  code: '37775-4',
  display: 'Breast - right Mammogram roll',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaRightXRayAPAndLateral: Coding = {
  code: '37776-2',
  display: 'Patella - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaRightXRay: Coding = {
  code: '37777-0',
  display: 'Patella - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PoplitealArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37778-8',
  display: 'Popliteal artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37779-6',
  display: 'Pulmonary artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsRightXRay2Views: Coding = {
  code: '37780-4',
  display: 'Ribs - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsRightXRay3Views: Coding = {
  code: '37781-2',
  display: 'Ribs - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsRightXRayAnteriorAndLateral: Coding = {
  code: '37782-0',
  display: 'Ribs - right X-ray anterior and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsRightXRayAPSingleView: Coding = {
  code: '37783-8',
  display: 'Ribs - right X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsRightXRayLateral: Coding = {
  code: '37784-6',
  display: 'Ribs - right X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointRightFluoroscopyWContrastIntraarticular: Coding = {
  code: '37785-3',
  display: 'Sacroiliac joint - right Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointRightXRay: Coding = {
  code: '37786-1',
  display: 'Sacroiliac joint - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaRightXRay2Views: Coding = {
  code: '37787-9',
  display: 'Scapula - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaRightXRayAPAndLateral: Coding = {
  code: '37788-7',
  display: 'Scapula - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaRightXRayAPAndY: Coding = {
  code: '37789-5',
  display: 'Scapula - right X-ray AP and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaRightXRayY: Coding = {
  code: '37790-3',
  display: 'Scapula - right X-ray Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayStrykerNotch: Coding = {
  code: '37791-1',
  display: 'Shoulder - right X-ray Stryker Notch',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRaySingleView: Coding = {
  code: '37792-9',
  display: 'Shoulder - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRay2Views: Coding = {
  code: '37793-7',
  display: 'Shoulder - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRay4Views: Coding = {
  code: '37794-5',
  display: 'Shoulder - right X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRay5Views: Coding = {
  code: '37795-2',
  display: 'Shoulder - right X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRay6Views: Coding = {
  code: '37796-0',
  display: 'Shoulder - right X-ray 6 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAPAndStrykerNotch: Coding = {
  code: '37797-8',
  display: 'Shoulder - right X-ray AP and Stryker Notch',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAPSingleView: Coding = {
  code: '37798-6',
  display: 'Shoulder - right X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAPAndWestPointAndOutlet: Coding = {
  code: '37799-4',
  display: 'Shoulder - right X-ray AP and West Point and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAxillary: Coding = {
  code: '37800-0',
  display: 'Shoulder - right X-ray axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayGarth: Coding = {
  code: '37801-8',
  display: 'Shoulder - right X-ray Garth',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayOutlet: Coding = {
  code: '37802-6',
  display: 'Shoulder - right X-ray outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayLateralAndY: Coding = {
  code: '37803-4',
  display: 'Shoulder - right X-ray lateral and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayOutletAndY: Coding = {
  code: '37804-2',
  display: 'Shoulder - right X-ray outlet and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayY: Coding = {
  code: '37805-9',
  display: 'Shoulder - right X-ray Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayGrasheyAndAxillaryAndOutlet: Coding = {
  code: '37806-7',
  display: 'Shoulder - right X-ray Grashey and axillary and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAxillaryAndY: Coding = {
  code: '37807-5',
  display: 'Shoulder - right X-ray axillary and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointRightXRaySerendipity: Coding = {
  code: '37808-3',
  display: 'Sternoclavicular joint - right X-ray Serendipity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayWestPoint: Coding = {
  code: '37809-1',
  display: 'Shoulder - right X-ray West Point',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointRightXRayZanca: Coding = {
  code: '37810-9',
  display: 'Acromioclavicular joint - right X-ray Zanca',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayTomograph: Coding = {
  code: '37811-7',
  display: 'Shoulder - right X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbRightXRay3Views: Coding = {
  code: '37812-5',
  display: 'Thumb - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbRightXRayAPAndLateralAndOblique: Coding = {
  code: '37813-3',
  display: 'Thumb - right X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbRightXRayWManualStress: Coding = {
  code: '37814-1',
  display: 'Thumb - right X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaRightAndFibulaRightXRay2Views: Coding = {
  code: '37815-8',
  display: 'Tibia - right and Fibula - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaRightAndFibulaRightXRayAPAndLateral: Coding = {
  code: '37816-6',
  display: 'Tibia - right and Fibula - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaRightAndFibulaRightXRayOblique: Coding = {
  code: '37817-4',
  display: 'Tibia - right and Fibula - right X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointRightFluoroscopyWContrastIntraarticular: Coding = {
  code: '37818-2',
  display: 'Temporomandibular joint - right Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointRightXRayTomograph: Coding = {
  code: '37819-0',
  display: 'Temporomandibular joint - right X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesRightXRay3Views: Coding = {
  code: '37820-8',
  display: 'Toes - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesRightXRay2Views: Coding = {
  code: '37821-6',
  display: 'Toes - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesRightXRayAPAndLateral: Coding = {
  code: '37822-4',
  display: 'Toes - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesRightXRayStanding: Coding = {
  code: '37823-2',
  display: 'Toes - right X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinsRightFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37824-0',
  display: 'Upper extremity veins - right Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRaySingleView: Coding = {
  code: '37825-7',
  display: 'Wrist - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRay2Views: Coding = {
  code: '37826-5',
  display: 'Wrist - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRay3Views: Coding = {
  code: '37827-3',
  display: 'Wrist - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRay4Views: Coding = {
  code: '37828-1',
  display: 'Wrist - right X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRay5Views: Coding = {
  code: '37829-9',
  display: 'Wrist - right X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRay6Views: Coding = {
  code: '37830-7',
  display: 'Wrist - right X-ray 6 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRay8Views: Coding = {
  code: '37831-5',
  display: 'Wrist - right X-ray 8 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayAPAndLateral: Coding = {
  code: '37832-3',
  display: 'Wrist - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayLateralWExtension: Coding = {
  code: '37833-1',
  display: 'Wrist - right X-ray lateral W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayLateralWFlexion: Coding = {
  code: '37834-9',
  display: 'Wrist - right X-ray lateral W flexion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayPAAndLateral: Coding = {
  code: '37835-6',
  display: 'Wrist - right X-ray PA and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayPAAndLateralAndOblique: Coding = {
  code: '37836-4',
  display: 'Wrist - right X-ray PA and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayAPAndLateralAndAxillary: Coding = {
  code: '37839-8',
  display: 'Shoulder X-ray AP and lateral and axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRay2Views: Coding = {
  code: '37840-6',
  display: 'Shoulder X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayAPAndLateral: Coding = {
  code: '37841-4',
  display: 'Shoulder X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayAPSingleView: Coding = {
  code: '37842-2',
  display: 'Shoulder X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayGarth: Coding = {
  code: '37843-0',
  display: 'Shoulder X-ray Garth',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayGrashey: Coding = {
  code: '37844-8',
  display: 'Shoulder X-ray Grashey',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayOutlet: Coding = {
  code: '37845-5',
  display: 'Shoulder X-ray outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointXRaySerendipity: Coding = {
  code: '37846-3',
  display: 'Sternoclavicular Joint X-ray Serendipity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayY: Coding = {
  code: '37847-1',
  display: 'Shoulder X-ray Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointXRayZanca: Coding = {
  code: '37848-9',
  display: 'Acromioclavicular Joint X-ray Zanca',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayAxillary: Coding = {
  code: '37849-7',
  display: 'Shoulder X-ray axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayTomograph: Coding = {
  code: '37850-5',
  display: 'Shoulder X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRaySingleView: Coding = {
  code: '37851-3',
  display: 'Sinuses X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayCaldwellAndWaters: Coding = {
  code: '37852-1',
  display: 'Sinuses X-ray Caldwell and Waters',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRay2Views: Coding = {
  code: '37853-9',
  display: 'Sinuses X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRay3Views: Coding = {
  code: '37854-7',
  display: 'Sinuses X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRay4Views: Coding = {
  code: '37855-4',
  display: 'Sinuses X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRay5Views: Coding = {
  code: '37856-2',
  display: 'Sinuses X-ray 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayCaldwell: Coding = {
  code: '37857-0',
  display: 'Sinuses X-ray Caldwell',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayLateral: Coding = {
  code: '37858-8',
  display: 'Sinuses X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayPAAndLateralAndWaters: Coding = {
  code: '37859-6',
  display: 'Sinuses X-ray PA and lateral and Waters',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayPAAndLateralAndCaldwellAndWaters: Coding = {
  code: '37860-4',
  display: 'Sinuses X-ray PA and lateral and Caldwell and Waters',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRaySubmentovertex: Coding = {
  code: '37861-2',
  display: 'Sinuses X-ray submentovertex',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayLateralAndWaters: Coding = {
  code: '37862-0',
  display: 'Sinuses X-ray lateral and Waters',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayWaters: Coding = {
  code: '37863-8',
  display: 'Sinuses X-ray Waters',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayLateralAndCaldwellAndWaters: Coding = {
  code: '37864-6',
  display: 'Sinuses X-ray lateral and Caldwell and Waters',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayTomograph: Coding = {
  code: '37866-1',
  display: 'Sinuses X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRay2Views: Coding = {
  code: '37867-9',
  display: 'Skull X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRay4Views: Coding = {
  code: '37868-7',
  display: 'Skull X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayLateralAndTowne: Coding = {
  code: '37869-5',
  display: 'Skull X-ray lateral and Towne',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayTowne: Coding = {
  code: '37870-3',
  display: 'Skull X-ray Towne',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayLateralAndCaldwellAndWatersAndTowne: Coding = {
  code: '37871-1',
  display: 'Skull X-ray lateral and Caldwell and Waters and Towne',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayLateralCrosstable: Coding = {
  code: '37872-9',
  display: 'Skull X-ray lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayTomograph: Coding = {
  code: '37874-5',
  display: 'Skull X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRaySingleView: Coding = {
  code: '37875-2',
  display: 'Spine X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRay4Views: Coding = {
  code: '37876-0',
  display: 'Spine X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRayAPSingleView: Coding = {
  code: '37877-8',
  display: 'Spine X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRayLateralCrosstable: Coding = {
  code: '37878-6',
  display: 'Spine X-ray lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRay2Views: Coding = {
  code: '37879-4',
  display: 'Spine X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointXRayAPSingleView: Coding = {
  code: '37880-2',
  display: 'Sternoclavicular Joint X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointXRay3Views: Coding = {
  code: '37881-0',
  display: 'Sternoclavicular Joint X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointXRay4Views: Coding = {
  code: '37882-8',
  display: 'Sternoclavicular Joint X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternumXRay2Views: Coding = {
  code: '37883-6',
  display: 'Sternum X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternumXRayPAAndLateralAndOblique: Coding = {
  code: '37884-4',
  display: 'Sternum X-ray PA and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternumXRayTomograph: Coding = {
  code: '37885-1',
  display: 'Sternum X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SubclavianArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37886-9',
  display: 'Subclavian artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForAspirationOfPleuralSpace: Coding = {
  code: '37887-7',
  display: 'Fluoroscopy Guidance for aspiration of Pleural space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbXRay3Views: Coding = {
  code: '37888-5',
  display: 'Thumb X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbXRayAPAndLateral: Coding = {
  code: '37889-3',
  display: 'Thumb X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbXRayAPSingleView: Coding = {
  code: '37890-1',
  display: 'Thumb X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbXRayLateral: Coding = {
  code: '37891-9',
  display: 'Thumb X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbXRayObliqueSingleView: Coding = {
  code: '37892-7',
  display: 'Thumb X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaAndFibulaXRayLateral: Coding = {
  code: '37893-5',
  display: 'Tibia and Fibula X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaAndFibulaXRaySingleView: Coding = {
  code: '37894-3',
  display: 'Tibia and Fibula X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaAndFibulaXRay2Views: Coding = {
  code: '37895-0',
  display: 'Tibia and Fibula X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaAndFibulaXRayAPAndLateral: Coding = {
  code: '37896-8',
  display: 'Tibia and Fibula X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaAndFibulaXRayAPSingleView: Coding = {
  code: '37897-6',
  display: 'Tibia and Fibula X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaAndFibulaXRayTomograph: Coding = {
  code: '37898-4',
  display: 'Tibia and Fibula X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaAndFibulaXRayStanding: Coding = {
  code: '37899-2',
  display: 'Tibia and Fibula X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibialArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37900-8',
  display: 'Tibial artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointFluoroscopyWContrastIntraarticular: Coding = {
  code: '37901-6',
  display: 'Temporomandibular joint Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesXRay2Views: Coding = {
  code: '37902-4',
  display: 'Toes X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayLateralCrosstable: Coding = {
  code: '37903-2',
  display: 'Spine Thoracic X-ray lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRaySingleView: Coding = {
  code: '37904-0',
  display: 'Spine Thoracic X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRay2Views: Coding = {
  code: '37905-7',
  display: 'Spine Thoracic X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRay3Views: Coding = {
  code: '37906-5',
  display: 'Spine Thoracic X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRay4Views: Coding = {
  code: '37907-3',
  display: 'Spine Thoracic X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayAPAndLateralAndOblique: Coding = {
  code: '37908-1',
  display: 'Spine Thoracic X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayLateralHyperextension: Coding = {
  code: '37909-9',
  display: 'Spine Thoracic X-ray lateral hyperextension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayLateralStanding: Coding = {
  code: '37910-7',
  display: 'Spine Thoracic X-ray lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayTomograph: Coding = {
  code: '37911-5',
  display: 'Spine Thoracic X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfBreastBilateral: Coding = {
  code: '37912-3',
  display: 'US Guidance for biopsy of Breast - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfAbdomen: Coding = {
  code: '37913-1',
  display: 'US Guidance for biopsy of Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfBreast: Coding = {
  code: '37914-9',
  display: 'US Guidance for biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfChest: Coding = {
  code: '37915-6',
  display: 'US Guidance for biopsy of Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfLymphNode: Coding = {
  code: '37916-4',
  display: 'US Guidance for needle biopsy of Lymph node',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfMuscle: Coding = {
  code: '37917-2',
  display: 'US Guidance for biopsy of Muscle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfNeck: Coding = {
  code: '37918-0',
  display: 'US Guidance for biopsy of Neck',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfPancreas: Coding = {
  code: '37919-8',
  display: 'US Guidance for biopsy of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfSalivaryGland: Coding = {
  code: '37920-6',
  display: 'US Guidance for biopsy of Salivary gland',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleLocalizationOfChest: Coding = {
  code: '37921-4',
  display: 'US Guidance for needle localization of Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityXRay2Views: Coding = {
  code: '37922-2',
  display: 'Upper extremity X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityXRayTomograph: Coding = {
  code: '37923-0',
  display: 'Upper extremity X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRaySingleView: Coding = {
  code: '37924-8',
  display: 'Wrist X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRay2Views: Coding = {
  code: '37925-5',
  display: 'Wrist X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRay3Views: Coding = {
  code: '37926-3',
  display: 'Wrist X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRayAPAndLateralAndOblique: Coding = {
  code: '37927-1',
  display: 'Wrist X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRayBrewerton: Coding = {
  code: '37928-9',
  display: 'Wrist X-ray Brewerton',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRayLateralWFlexionAndWExtension: Coding = {
  code: '37929-7',
  display: 'Wrist X-ray lateral W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRayLateral: Coding = {
  code: '37930-5',
  display: 'Wrist X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRayPA: Coding = {
  code: '37931-3',
  display: 'Wrist X-ray PA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRayTomograph: Coding = {
  code: '37932-1',
  display: 'Wrist X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ZygomaticArchXRay3Views: Coding = {
  code: '37933-9',
  display: 'Zygomatic arch X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ZygomaticArchXRay4Views: Coding = {
  code: '37934-7',
  display: 'Zygomatic arch X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisArteriesAndLowerExtremityArteriesBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37935-4',
  display: 'Pelvis arteries and Lower extremity arteries - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeripheralVesselsFluoroscopicAngiogramWContrast: Coding = {
  code: '37936-2',
  display: 'Peripheral vessels Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsAnteriorXRay: Coding = {
  code: '37937-0',
  display: 'Ribs anterior X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsPosteriorXRay: Coding = {
  code: '37938-8',
  display: 'Ribs posterior X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37939-6',
  display: 'Adrenal artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalVeinRightFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37940-4',
  display: 'Adrenal vein - right Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleArteriesRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37941-2',
  display: 'Ankle arteries - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightFluoroscopyWContrastIntraarticular: Coding = {
  code: '37942-0',
  display: 'Ankle - right Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryPlusVertebralArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37943-8',
  display: 'Carotid artery+Vertebral artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryAndCerebralArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37944-6',
  display: 'Carotid artery and Cerebral artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryCervicalRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37945-3',
  display: 'Carotid artery.cervical - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightFluoroscopyWContrastIntraarticular: Coding = {
  code: '37947-9',
  display: 'Elbow - right Fluoroscopy W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryExternalRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37948-7',
  display: 'Carotid artery.external - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteriesRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37949-5',
  display: 'Extremity arteries - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinsRightFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37950-3',
  display: 'Extremity veins - right Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryInternalRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37952-9',
  display: 'Carotid artery.internal - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryAndCerebralArteryInternalRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37953-7',
  display: 'Carotid artery and Cerebral artery internal - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JugularVeinRightFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37954-5',
  display: 'Jugular vein - right Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitVeinsRightFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37958-6',
  display: 'Orbit veins - right Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVeinRightFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37959-4',
  display: 'Renal vein - right Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLowerRightXRay: Coding = {
  code: '37960-2',
  display: 'Ribs lower - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsUpperRightXRay: Coding = {
  code: '37961-0',
  display: 'Ribs upper - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsAnteriorAndPosteriorRightXRay: Coding = {
  code: '37962-8',
  display: 'Ribs anterior and posterior - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsAnteriorRightXRay: Coding = {
  code: '37963-6',
  display: 'Ribs anterior - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsPosteriorRightXRay: Coding = {
  code: '37964-4',
  display: 'Ribs posterior - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointRightXRay: Coding = {
  code: '37965-1',
  display: 'Sternoclavicular joint - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SubclavianArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37966-9',
  display: 'Subclavian artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteriesRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37967-7',
  display: 'Upper extremity arteries - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VertebralArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37968-5',
  display: 'Vertebral artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusVeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37969-3',
  display: 'Sinus vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SplenicVeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37970-1',
  display: 'Splenic vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SubclavianVeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37971-9',
  display: 'Subclavian vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SuperiorMesentericVeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '37972-7',
  display: 'Superior mesenteric vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TesticleVesselsFluoroscopyWContrast: Coding = {
  code: '37973-5',
  display: 'Testicle vessels Fluoroscopy W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracolumbarJunctionXRayAPAndLateral: Coding = {
  code: '37974-3',
  display: 'Spine Thoracolumbar Junction X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracolumbarJunctionXRay: Coding = {
  code: '37975-0',
  display: 'Spine Thoracolumbar Junction X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsFluoroscopicAngiogramWContrast: Coding = {
  code: '37976-8',
  display: 'Upper extremity vessels Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteriesFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37977-6',
  display: 'Upper extremity arteries Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UterineArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '37979-2',
  display: 'Uterine artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VertebralVesselsFluoroscopicAngiogramWContrast: Coding = {
  code: '37980-0',
  display: 'Vertebral vessels Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VisceralVesselsFluoroscopicAngiogramWContrast: Coding = {
  code: '37981-8',
  display: 'Visceral vessels Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LumbarSpineVesselsMRIAngiogramWOContrast: Coding = {
  code: '37994-1',
  display: 'Lumbar Spine vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusBilateralXRayBroden: Coding = {
  code: '37995-8',
  display: 'Calcaneus - bilateral X-ray Broden',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusXRayBroden: Coding = {
  code: '37996-6',
  display: 'Calcaneus X-ray Broden',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusLeftXRayBroden: Coding = {
  code: '37997-4',
  display: 'Calcaneus - left X-ray Broden',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowXRayRadialHeadCapitellar: Coding = {
  code: '37998-2',
  display: 'Elbow X-ray radial head capitellar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralXRayRadialHeadCapitellar: Coding = {
  code: '37999-0',
  display: 'Elbow - bilateral X-ray radial head capitellar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRayRadialHeadCapitellar: Coding = {
  code: '38000-6',
  display: 'Elbow - left X-ray radial head capitellar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayWExpiration: Coding = {
  code: '38001-4',
  display: 'Chest X-ray W expiration',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayWInspiration: Coding = {
  code: '38002-2',
  display: 'Chest X-ray W inspiration',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRayAPStanding: Coding = {
  code: '38003-0',
  display: 'Foot - left X-ray AP standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayGrasheyWAndWOWeight: Coding = {
  code: '38004-8',
  display: 'Shoulder - left X-ray Grashey W and WO weight',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRayRadialHeadCapitellar: Coding = {
  code: '38006-3',
  display: 'Elbow - right X-ray radial head capitellar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusRightXRayTransthoracic: Coding = {
  code: '38007-1',
  display: 'Humerus - right X-ray transthoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndThoracicAndLumbarXRay: Coding = {
  code: '38008-9',
  display: 'Spine Cervical and Thoracic and Lumbar X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayAPAndLateralAndSwimmers: Coding = {
  code: '38009-7',
  display: 'Spine Thoracic X-ray AP and lateral and Swimmers',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayLateralWFlexionAndWExtension: Coding = {
  code: '38010-5',
  display: 'Spine Thoracic X-ray lateral W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaUSLimited: Coding = {
  code: '38011-3',
  display: 'Aorta US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfCystOfBreastBilateral: Coding = {
  code: '38012-1',
  display: 'US Guidance for aspiration of cyst of Breast - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityBilateralUS: Coding = {
  code: '38013-9',
  display: 'Lower extremity - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteryBilateralUS: Coding = {
  code: '38014-7',
  display: 'Upper extremity artery - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryUSLimited: Coding = {
  code: '38015-4',
  display: 'Carotid artery US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestWallUS: Coding = {
  code: '38016-2',
  display: 'Chest wall US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForFineNeedleAspirationOfProstate: Coding = {
  code: '38017-0',
  display: 'US Guidance for fine needle aspiration of Prostate',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForFineNeedleAspirationOfUnspecifiedBodyRegion: Coding = {
  code: '38018-8',
  display: 'US Guidance for fine needle aspiration of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForFineNeedleAspirationOfThyroid: Coding = {
  code: '38019-6',
  display: 'US Guidance for fine needle aspiration of Thyroid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GallbladderUSLimited: Coding = {
  code: '38020-4',
  display: 'Gallbladder US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BiliaryDuctsAndGallbladderUS: Coding = {
  code: '38021-2',
  display: 'Biliary ducts and Gallbladder US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GallbladderUSWCholecystokinin: Coding = {
  code: '38022-0',
  display: 'Gallbladder US W cholecystokinin',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForCoreNeedlePercutaneousBiopsyOfBreastLeft: Coding = {
  code: '38023-8',
  display: 'US Guidance for core needle percutaneous biopsy of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForCoreNeedleBiopsyOfUnspecifiedBodyRegion: Coding = {
  code: '38024-6',
  display: 'US Guidance for core needle biopsy of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForCoreNeedlePercutaneousBiopsyOfBreastRight: Coding = {
  code: '38025-3',
  display: 'US Guidance for core needle percutaneous biopsy of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForFineNeedleAspirationOfBreastLeft: Coding = {
  code: '38026-1',
  display: 'US Guidance for fine needle aspiration of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfKidneyBilateral: Coding = {
  code: '38027-9',
  display: 'US Guidance for needle biopsy of Kidney - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfBreast: Coding = {
  code: '38028-7',
  display: 'US Guidance for needle biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfChest: Coding = {
  code: '38029-5',
  display: 'US Guidance for needle biopsy of Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfSpleen: Coding = {
  code: '38030-3',
  display: 'US Guidance for needle biopsy of Spleen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfThyroid: Coding = {
  code: '38031-1',
  display: 'US Guidance for needle biopsy of Thyroid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleLocalizationOfUnspecifiedBodyRegion: Coding = {
  code: '38032-9',
  display: 'US Guidance for needle localization of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForFineNeedleAspirationOfBreastRight: Coding = {
  code: '38033-7',
  display: 'US Guidance for fine needle aspiration of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadUSLimited: Coding = {
  code: '38034-5',
  display: 'Head US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyUSLimited: Coding = {
  code: '38035-2',
  display: 'Kidney US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyUS: Coding = {
  code: '38036-0',
  display: 'Kidney US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurLeftUS: Coding = {
  code: '38037-8',
  display: 'Femur - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyLeftUS: Coding = {
  code: '38038-6',
  display: 'Kidney - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftUSLimited: Coding = {
  code: '38039-4',
  display: 'Lower extremity - left US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftUS: Coding = {
  code: '38040-2',
  display: 'Lower extremity - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityLeftUS: Coding = {
  code: '38041-0',
  display: 'Upper extremity - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityArteryUSDopplerLimited: Coding = {
  code: '38042-8',
  display: 'Lower extremity artery US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidUS: Coding = {
  code: '38043-6',
  display: 'Mastoid US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MediastinumUS: Coding = {
  code: '38044-4',
  display: 'Mediastinum US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParathyroidUS: Coding = {
  code: '38045-1',
  display: 'Parathyroid US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisUSLimited: Coding = {
  code: '38046-9',
  display: 'Pelvis US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenRetroperitoneumUSLimited: Coding = {
  code: '38047-7',
  display: 'Abdomen retroperitoneum US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightUS: Coding = {
  code: '38048-5',
  display: 'Femur - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyRightUS: Coding = {
  code: '38049-3',
  display: 'Kidney - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightUSLimited: Coding = {
  code: '38050-1',
  display: 'Lower extremity - right US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightUS: Coding = {
  code: '38051-9',
  display: 'Lower extremity - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityRightUS: Coding = {
  code: '38052-7',
  display: 'Upper extremity - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumUS: Coding = {
  code: '38053-5',
  display: 'Sacrum US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VisceralArteryUS: Coding = {
  code: '38054-3',
  display: 'Visceral artery US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionUSWContrast: Coding = {
  code: '38055-0',
  display: 'Unspecified body region US W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertStructuredProductLaballingSupplementalPatientMaterial: Coding = {
  code: '38056-8',
  display: 'FDA package insert Structured product laballing supplemental patient material',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantLeftMRI: Coding = {
  code: '38057-6',
  display: 'Breast implant - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantRightMRI: Coding = {
  code: '38058-4',
  display: 'Breast implant - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TalusCT: Coding = {
  code: '38059-2',
  display: 'Talus CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbosacralPlusCervicalPlusThoracicMRISagittal: Coding = {
  code: '38060-0',
  display: 'Spine.lumbosacral+Cervical+Thoracic MRI sagittal',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndSpineThoracicAndSpineLumbarAndSacrumMRIWContrastIV: Coding = {
  code: '38061-8',
  display: 'Spine Cervical and Spine Thoracic and Spine Lumbar and Sacrum MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantRightMRIWAndWOContrastIV: Coding = {
  code: '38062-6',
  display: 'Breast implant - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantLeftMRIWOContrast: Coding = {
  code: '38064-2',
  display: 'Breast implant - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayDuringSurgery: Coding = {
  code: '38065-9',
  display: 'Hip - left X-ray during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayLateralDuringSurgery: Coding = {
  code: '38066-7',
  display: 'Hip - left X-ray lateral during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramNippleProfile: Coding = {
  code: '38067-5',
  display: 'Breast - bilateral Mammogram nipple profile',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayRightAnteriorOblique: Coding = {
  code: '38068-3',
  display: 'Chest X-ray right anterior oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayLeftPosteriorOblique: Coding = {
  code: '38069-1',
  display: 'Abdomen X-ray left posterior oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantMammogram: Coding = {
  code: '38070-9',
  display: 'Breast implant Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantBilateralMammogram: Coding = {
  code: '38071-7',
  display: 'Breast implant - bilateral Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantLeftMammogram: Coding = {
  code: '38072-5',
  display: 'Breast implant - left Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsAnteriorBilateralXRay: Coding = {
  code: '38073-3',
  display: 'Ribs anterior - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsAnteriorLeftXRay: Coding = {
  code: '38074-1',
  display: 'Ribs anterior - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastSpecimenBilateralMammogram: Coding = {
  code: '38079-0',
  display: 'Breast specimen - bilateral Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastSpecimenLeftMammogram: Coding = {
  code: '38080-8',
  display: 'Breast specimen - left Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPAndTransthoracic: Coding = {
  code: '38082-4',
  display: 'Shoulder - left X-ray AP and transthoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPAndLateralAndObliqueAndOdontoidAndSwimmer: Coding = {
  code: '38083-2',
  display: 'Spine Cervical X-ray AP and lateral and oblique and odontoid and swimmer',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPAndLeftPosteriorOblique: Coding = {
  code: '38084-0',
  display: 'Abdomen X-ray AP and left posterior oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayMerchants30And45And60Degrees: Coding = {
  code: '38086-5',
  display: 'Knee X-ray Merchants 30 and 45 and 60 degrees',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRaySunrise20And40And60Degrees: Coding = {
  code: '38087-3',
  display: 'Knee - left X-ray Sunrise 20 and 40 and 60 degrees',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRaySunrise20And40And60Degrees: Coding = {
  code: '38088-1',
  display: 'Knee - bilateral X-ray Sunrise 20 and 40 and 60 degrees',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BonesXRaySurveyLimitedForMetastasis: Coding = {
  code: '38089-9',
  display: 'Bones X-ray survey limited for metastasis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramWAir: Coding = {
  code: '38090-7',
  display: 'Breast - bilateral Mammogram W air',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramWAir: Coding = {
  code: '38091-5',
  display: 'Breast - left Mammogram W air',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrinaryBladderFluoroscopyWChainAndContrastIntraBladder: Coding = {
  code: '38092-3',
  display: 'Urinary bladder Fluoroscopy W chain and contrast intra bladder',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayWNippleMarkers: Coding = {
  code: '38093-1',
  display: 'Chest X-ray W nipple markers',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCavityFluoroscopyWContrast: Coding = {
  code: '38094-9',
  display: 'Spine.cavity Fluoroscopy W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastDuctBilateralMammogramWContrastIntraDuct: Coding = {
  code: '38095-6',
  display: 'Breast duct - bilateral Mammogram W contrast intra duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastDuctLeftMammogramWContrastIntraDuct: Coding = {
  code: '38096-4',
  display: 'Breast duct - left Mammogram W contrast intra duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandLeftFluoroscopyWContrastIntraSalivaryDuct: Coding = {
  code: '38097-2',
  display: 'Parotid gland - left Fluoroscopy W contrast intra salivary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LacrimalDuctBilateralFluoroscopyWContrastIntraLacrimalDuct: Coding = {
  code: '38098-0',
  display: 'Lacrimal duct - bilateral Fluoroscopy W contrast intra lacrimal duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LacrimalDuctLeftFluoroscopyWContrastIntraLacrimalDuct: Coding = {
  code: '38099-8',
  display: 'Lacrimal duct - left Fluoroscopy W contrast intra lacrimal duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrinaryBladderAndUrethraFluoroscopyWContrastAntegrade: Coding = {
  code: '38100-4',
  display: 'Urinary Bladder and Urethra Fluoroscopy W contrast antegrade',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyXRayWContrastAntegrade: Coding = {
  code: '38101-2',
  display: 'Kidney X-ray W contrast antegrade',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyXRayWContrastAntegradeViaPyelostomy: Coding = {
  code: '38102-0',
  display: 'Kidney X-ray W contrast antegrade via pyelostomy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndSpineLumbarFluoroscopyWContrastIT: Coding = {
  code: '38103-8',
  display: 'Spine Cervical and Spine Lumbar Fluoroscopy W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineEpiduralSpaceFluoroscopyWContrastIT: Coding = {
  code: '38104-6',
  display: 'Spine.epidural space Fluoroscopy W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyXRayWContrastRetrograde: Coding = {
  code: '38105-3',
  display: 'Kidney X-ray W contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRayScaphoid: Coding = {
  code: '38107-9',
  display: 'Wrist X-ray scaphoid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay2ViewsOblique: Coding = {
  code: '38108-7',
  display: 'Knee - right X-ray 2 views Oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyRightAndCollectingSystemFluoroscopyWContrastViaNephrostomyTube: Coding = {
  code: '38112-9',
  display: 'Kidney - right and Collecting system Fluoroscopy W contrast via nephrostomy tube',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyRightAndCollectingSystemFluoroscopyWContrastRetrogradeViaUrethra: Coding = {
  code: '38113-7',
  display: 'Kidney - right and Collecting system Fluoroscopy W contrast retrograde via urethra',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaRightAndFibulaRightXRay2ViewsOblique: Coding = {
  code: '38114-5',
  display: 'Tibia - right and Fibula - right X-ray 2 views Oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayScaphoid: Coding = {
  code: '38115-2',
  display: 'Wrist - right X-ray scaphoid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandFluoroscopyWContrastIntraSalivaryDuct: Coding = {
  code: '38116-0',
  display: 'Parotid gland Fluoroscopy W contrast intra salivary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayWatersUpright: Coding = {
  code: '38117-8',
  display: 'Sinuses X-ray Waters upright',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckXRay2ViewsLateral: Coding = {
  code: '38118-6',
  display: 'Neck X-ray 2 views lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '38119-4',
  display: 'Thoracic artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicFluoroscopyLimitedWContrastIT: Coding = {
  code: '38120-2',
  display: 'Spine Thoracic Fluoroscopy limited W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRaySingleView: Coding = {
  code: '38121-0',
  display: 'Spine Thoracic and Lumbar X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayAPAndLateral: Coding = {
  code: '38123-6',
  display: 'Spine Thoracic and Lumbar X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayStanding: Coding = {
  code: '38124-4',
  display: 'Spine Thoracic and Lumbar X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndThoracicAndLumbarFluoroscopyLimitedWContrastIT: Coding = {
  code: '38125-1',
  display: 'Spine Cervical and Thoracic and Lumbar Fluoroscopy limited W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfCystOfKidney: Coding = {
  code: '38126-9',
  display: 'US Guidance for aspiration of cyst of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForCSFAspirationOfSpine: Coding = {
  code: '38127-7',
  display: 'US Guidance for CSF aspiration of Spine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralVesselsBilateralUS: Coding = {
  code: '38128-5',
  display: 'Femoral vessels - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacVesselsBilateralUS: Coding = {
  code: '38129-3',
  display: 'Iliac vessels - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityArteryBilateralUS: Coding = {
  code: '38130-1',
  display: 'Lower extremity artery - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SubclavianVesselsBilateralUS: Coding = {
  code: '38131-9',
  display: 'Subclavian vessels - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfScrotumAndTesticle: Coding = {
  code: '38132-7',
  display: 'US Guidance for biopsy of Scrotum and Testicle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfCystOfPancreas: Coding = {
  code: '38133-5',
  display: 'US Guidance for aspiration of cyst of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralVesselsUS: Coding = {
  code: '38134-3',
  display: 'Femoral vessels US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForFineNeedleAspirationOfDeepTissue: Coding = {
  code: '38135-0',
  display: 'US Guidance for fine needle aspiration of Deep tissue',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForFineNeedleAspirationOfSuperficialTissue: Coding = {
  code: '38136-8',
  display: 'US Guidance for fine needle aspiration of Superficial tissue',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacVesselsLeftUS: Coding = {
  code: '38137-6',
  display: 'Iliac vessels - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandUS: Coding = {
  code: '38138-4',
  display: 'Parotid gland US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PenisVesselsUS: Coding = {
  code: '38139-2',
  display: 'Penis vessels US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PenisUS: Coding = {
  code: '38140-0',
  display: 'Penis US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacVesselsRightUS: Coding = {
  code: '38141-8',
  display: 'Iliac vessels - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForRemovalOfFluidFromChest: Coding = {
  code: '38142-6',
  display: 'US Guidance for removal of fluid from Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteryUSDopplerLimited: Coding = {
  code: '38143-4',
  display: 'Upper extremity artery US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerSecondRightXRay: Coding = {
  code: '38144-2',
  display: 'Finger second - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerThirdRightXRay: Coding = {
  code: '38145-9',
  display: 'Finger third - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFourthRightXRay: Coding = {
  code: '38146-7',
  display: 'Finger fourth - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFifthRightXRay: Coding = {
  code: '38147-5',
  display: 'Finger fifth - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToeSecondRightXRay: Coding = {
  code: '38148-3',
  display: 'Toe second - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToeThirdRightXRay: Coding = {
  code: '38149-1',
  display: 'Toe third - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToeFourthRightXRay: Coding = {
  code: '38150-9',
  display: 'Toe fourth - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToeFifthRightXRay: Coding = {
  code: '38151-7',
  display: 'Toe fifth - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GreatToeRightXRay: Coding = {
  code: '38152-5',
  display: 'Great toe - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SubmandibularGlandFluoroscopyWContrastIntraSalivaryDuct: Coding = {
  code: '38153-3',
  display: 'Submandibular gland Fluoroscopy W contrast intra salivary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfSuperficialBone: Coding = {
  code: '38154-1',
  display: 'Fluoroscopy Guidance for biopsy of Superficial bone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRay4Views: Coding = {
  code: '38155-8',
  display: 'Wrist X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRay6Views: Coding = {
  code: '38156-6',
  display: 'Wrist X-ray 6 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkeletalSystemDXABoneDensity: Coding = {
  code: '38268-9',
  display: 'Skeletal system DXA Bone density',
  system: 'http://loinc.org',
};
const C80DocTypecodes_StudyReportSkeletalSystemDXA: Coding = {
  code: '38269-7',
  display: 'Study report Skeletal system DXA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfLiverTransplant: Coding = {
  code: '38765-4',
  display: 'US Guidance for biopsy of Liver transplant',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfKidneyTransplant: Coding = {
  code: '38766-2',
  display: 'US Guidance for biopsy of Kidney transplant',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalRightCT: Coding = {
  code: '38767-0',
  display: 'Internal auditory canal - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightXRayTomograph: Coding = {
  code: '38768-8',
  display: 'Femur - right X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointRightMRILimitedWOContrast: Coding = {
  code: '38769-6',
  display: 'Lower extremity joint - right MRI limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaRightMRIWOContrast: Coding = {
  code: '38770-4',
  display: 'Scapula - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipRightXRay: Coding = {
  code: '38771-2',
  display: 'Pelvis and Hip - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayTrueLateral: Coding = {
  code: '38772-0',
  display: 'Hip - right X-ray true lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsRightMRIAngiogramWOContrast: Coding = {
  code: '38773-8',
  display: 'Lower extremity vessels - right MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitRightXRay: Coding = {
  code: '38774-6',
  display: 'Orbit - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRayBrewerton: Coding = {
  code: '38775-3',
  display: 'Hand - right X-ray Brewerton',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusRightXRayHarris: Coding = {
  code: '38776-1',
  display: 'Calcaneus - right X-ray Harris',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRayJones: Coding = {
  code: '38777-9',
  display: 'Elbow - right X-ray Jones',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusRightXRaySkiJump: Coding = {
  code: '38778-7',
  display: 'Calcaneus - right X-ray ski jump',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayTransthoracic: Coding = {
  code: '38779-5',
  display: 'Shoulder - right X-ray transthoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayVelpeauAxillary: Coding = {
  code: '38780-3',
  display: 'Shoulder - right X-ray Velpeau axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAPAndAxillaryAndOutlet: Coding = {
  code: '38781-1',
  display: 'Shoulder - right X-ray AP and axillary and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAPAndAxillaryAndOutletAndZanca: Coding = {
  code: '38782-9',
  display: 'Shoulder - right X-ray AP and axillary and outlet and Zanca',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAPAndAxillaryAndY: Coding = {
  code: '38783-7',
  display: 'Shoulder - right X-ray AP and axillary and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipRightXRayAPAndLateralCrosstable: Coding = {
  code: '38784-5',
  display: 'Pelvis and Hip - right X-ray AP and lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipRightXRayAPAndLateralFrog: Coding = {
  code: '38785-2',
  display: 'Pelvis and Hip - right X-ray AP and lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaRightXRayAPAndLateralAndSunrise: Coding = {
  code: '38786-0',
  display: 'Patella - right X-ray AP and lateral and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAPAndWestPoint: Coding = {
  code: '38787-8',
  display: 'Shoulder - right X-ray AP and West Point',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAPAndY: Coding = {
  code: '38788-6',
  display: 'Shoulder - right X-ray AP and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayGrasheyAndAxillaryAndY: Coding = {
  code: '38789-4',
  display: 'Shoulder - right X-ray Grashey and axillary and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaRightXRayPAAndLateralAndSunrise: Coding = {
  code: '38790-2',
  display: 'Patella - right X-ray PA and lateral and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayGrasheyAndOutlet: Coding = {
  code: '38791-0',
  display: 'Shoulder - right X-ray Grashey and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRayTarsal: Coding = {
  code: '38792-8',
  display: 'Foot - right X-ray tarsal',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayGrasheyAndAxillary: Coding = {
  code: '38793-6',
  display: 'Shoulder - right X-ray Grashey and axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayGrasheyAndOutletAndSerendipity: Coding = {
  code: '38794-4',
  display: 'Shoulder - right X-ray Grashey and outlet and Serendipity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayGrasheyAndWestPoint: Coding = {
  code: '38795-1',
  display: 'Shoulder - right X-ray Grashey and West Point',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayPortable: Coding = {
  code: '38796-9',
  display: 'Hip - right X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusBicipitalGrooveRightXRay: Coding = {
  code: '38797-7',
  display: 'Humerus bicipital groove - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OlecranonRightXRay: Coding = {
  code: '38798-5',
  display: 'Olecranon - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAndFemoralArteryRightFluoroscopicAngiogramRunoffWContrastIA: Coding = {
  code: '38799-3',
  display: 'Aorta and Femoral artery - right Fluoroscopic angiogram runoff W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchAndSubclavianArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '38800-9',
  display: 'Aortic arch and Subclavian artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastricArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '38801-7',
  display: 'Gastric artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightCTWAndWOContrastIV: Coding = {
  code: '38802-5',
  display: 'Wrist - right CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleRightXRay45DegreeCephalicAngle: Coding = {
  code: '38803-3',
  display: 'Clavicle - right X-ray 45 degree cephalic angle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRayBora: Coding = {
  code: '38804-1',
  display: 'Hand - right X-ray Bora',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayGrashey: Coding = {
  code: '38805-8',
  display: 'Shoulder - right X-ray Grashey',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaRightXRay10DegreeCaudalAngle: Coding = {
  code: '38806-6',
  display: 'Tibia - right X-ray 10 degree caudal angle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogramSpot: Coding = {
  code: '38807-4',
  display: 'Breast - right Mammogram spot',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayUlnarDeviationAndRadialDeviation: Coding = {
  code: '38808-2',
  display: 'Wrist - right X-ray ulnar deviation and radial deviation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GreatToeRightXRayStanding: Coding = {
  code: '38810-8',
  display: 'Great toe - right X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LymphaticsAbdominalAndLymphaticsPelvicRightFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '38811-6',
  display: 'Lymphatics abdominal and Lymphatics pelvic - right Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityLymphaticsRightFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '38812-4',
  display: 'Extremity lymphatics - right Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LymphaticsRightFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '38813-2',
  display: 'Lymphatics - right Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusRightXRayBroden: Coding = {
  code: '38814-0',
  display: 'Calcaneus - right X-ray Broden',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRayAPStanding: Coding = {
  code: '38815-7',
  display: 'Foot - right X-ray AP standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayGrasheyWAndWOWeight: Coding = {
  code: '38816-5',
  display: 'Shoulder - right X-ray Grashey W and WO weight',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantRightMRIWOContrast: Coding = {
  code: '38817-3',
  display: 'Breast implant - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayDuringSurgery: Coding = {
  code: '38818-1',
  display: 'Hip - right X-ray during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayLateralDuringSurgery: Coding = {
  code: '38819-9',
  display: 'Hip - right X-ray lateral during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantRightMammogram: Coding = {
  code: '38820-7',
  display: 'Breast implant - right Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastSpecimenRightMammogram: Coding = {
  code: '38821-5',
  display: 'Breast specimen - right Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAPAndTransthoracic: Coding = {
  code: '38822-3',
  display: 'Shoulder - right X-ray AP and transthoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRaySunrise20And40And60Degrees: Coding = {
  code: '38824-9',
  display: 'Knee - right X-ray Sunrise 20 and 40 and 60 degrees',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastDuctRightMammogramWContrastIntraDuct: Coding = {
  code: '38825-6',
  display: 'Breast duct - right Mammogram W contrast intra duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandRightFluoroscopyWContrastIntraSalivaryDuct: Coding = {
  code: '38826-4',
  display: 'Parotid gland - right Fluoroscopy W contrast intra salivary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LacrimalDuctRightFluoroscopyWContrastIntraLacrimalDuct: Coding = {
  code: '38827-2',
  display: 'Lacrimal duct - right Fluoroscopy W contrast intra lacrimal duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftCTWContrastIntraarticular: Coding = {
  code: '38828-0',
  display: 'Shoulder - left CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityLeftMRIWContrastIV: Coding = {
  code: '38829-8',
  display: 'Upper extremity - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftMRIWContrastIV: Coding = {
  code: '38830-6',
  display: 'Shoulder - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityLeftMRIWAndWOContrastIV: Coding = {
  code: '38831-4',
  display: 'Upper extremity - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityLeftMRIWOContrast: Coding = {
  code: '38832-2',
  display: 'Upper extremity - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletLeftMRIWOContrast: Coding = {
  code: '38833-0',
  display: 'Thoracic outlet - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftMRIWOContrast: Coding = {
  code: '38834-8',
  display: 'Shoulder - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporalBoneLeftCTWContrastIV: Coding = {
  code: '38835-5',
  display: 'Temporal bone - left CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitLeftMRI: Coding = {
  code: '38836-3',
  display: 'Orbit - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeVesselsLeftMRIAngiogramWAndWOContrastIV: Coding = {
  code: '38837-1',
  display: 'Knee vessels - left MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayLimited: Coding = {
  code: '38838-9',
  display: 'Wrist - left X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayOblique: Coding = {
  code: '38839-7',
  display: 'Wrist - left X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRay2ViewsWManualStress: Coding = {
  code: '38840-5',
  display: 'Ankle - left X-ray 2 views W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRay2ViewsStanding: Coding = {
  code: '38841-3',
  display: 'Ankle - left X-ray 2 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayTunnelCarpal: Coding = {
  code: '38842-1',
  display: 'Wrist - left X-ray tunnel.carpal',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRay2ViewsTunnelCarpal: Coding = {
  code: '38843-9',
  display: 'Wrist - left X-ray 2 views tunnel.carpal',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRay2ViewsOblique: Coding = {
  code: '38844-7',
  display: 'Elbow - left X-ray 2 views Oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurLeftXRayStanding: Coding = {
  code: '38845-4',
  display: 'Femur - left X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRay2Views: Coding = {
  code: '38846-2',
  display: 'Foot - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRayAPAndLateral: Coding = {
  code: '38847-0',
  display: 'Hand - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRayAPAndLateralAndOblique: Coding = {
  code: '38848-8',
  display: 'Hand - left X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftXRayAPSingleViewStanding: Coding = {
  code: '38849-6',
  display: 'Lower extremity - left X-ray AP single view standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityLeftXRayStanding: Coding = {
  code: '38850-4',
  display: 'Lower extremity - left X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay2ViewsStanding: Coding = {
  code: '38851-2',
  display: 'Knee - left X-ray 2 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay4ViewsStanding: Coding = {
  code: '38852-0',
  display: 'Knee - left X-ray 4 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsLeftFluoroscopicAngiogramWContrast: Coding = {
  code: '38853-8',
  display: 'Lower extremity vessels - left Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramMagnificationAndSpot: Coding = {
  code: '38854-6',
  display: 'Breast - left Mammogram magnification and spot',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramTrueLateral: Coding = {
  code: '38855-3',
  display: 'Breast - left Mammogram true lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLeftXRayAnteriorAndLateral: Coding = {
  code: '38856-1',
  display: 'Ribs - left X-ray anterior and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLeftXRayLateral: Coding = {
  code: '38857-9',
  display: 'Ribs - left X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayY: Coding = {
  code: '38858-7',
  display: 'Shoulder - left X-ray Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinsLeftFluoroscopicAngiogramWContrastIV: Coding = {
  code: '38859-5',
  display: 'Upper extremity veins - left Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayAPAndLateral: Coding = {
  code: '38860-3',
  display: 'Wrist - left X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleArteriesLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '38861-1',
  display: 'Ankle arteries - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryAndCerebralArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '38862-9',
  display: 'Carotid artery and Cerebral artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryCervicalLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '38863-7',
  display: 'Carotid artery.cervical - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryInternalLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '38864-5',
  display: 'Carotid artery.internal - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryAndCerebralArteryInternalLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '38865-2',
  display: 'Carotid artery and Cerebral artery internal - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLowerLeftXRay: Coding = {
  code: '38866-0',
  display: 'Ribs lower - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsUpperLeftXRay: Coding = {
  code: '38867-8',
  display: 'Ribs upper - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsAnteriorAndPosteriorLeftXRay: Coding = {
  code: '38868-6',
  display: 'Ribs anterior and posterior - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsPosteriorLeftXRay: Coding = {
  code: '38869-4',
  display: 'Ribs posterior - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantLeftMRIWAndWOContrastIV: Coding = {
  code: '38870-2',
  display: 'Breast implant - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay2ViewsOblique: Coding = {
  code: '38871-0',
  display: 'Knee - left X-ray 2 views Oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyLeftAndCollectingSystemFluoroscopyWContrastViaNephrostomyTube: Coding = {
  code: '38872-8',
  display: 'Kidney - left and Collecting system Fluoroscopy W contrast via nephrostomy tube',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyLeftAndCollectingSystemFluoroscopyWContrastRetrogradeViaUrethra: Coding = {
  code: '38873-6',
  display: 'Kidney - left and Collecting system Fluoroscopy W contrast retrograde via urethra',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaLeftAndFibulaLeftXRay2ViewsOblique: Coding = {
  code: '38874-4',
  display: 'Tibia - left and Fibula - left X-ray 2 views Oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationAcromegaly: Coding = {
  code: '38932-0',
  display: 'VA Compensation and Pension (C and P) examination acromegaly',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationAidAndAttendanceHousebound: Coding = {
  code: '38933-8',
  display: 'VA Compensation and Pension (C and P) examination aid and attendance/housebound',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationArrhythmias: Coding = {
  code: '38934-6',
  display: 'VA Compensation and Pension (C and P) examination arrhythmias',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationMiscellaneousArteriesVeins: Coding = {
  code: '38935-3',
  display: 'VA Compensation and Pension (C and P) examination miscellaneous arteries/veins',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationAudio: Coding = {
  code: '38936-1',
  display: 'VA Compensation and Pension (C and P) examination audio',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationBonesFracturesBoneDisease: Coding = {
  code: '38937-9',
  display: 'VA Compensation and Pension (C and P) examination bones fractures/bone disease',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationBrainSpinalCord: Coding = {
  code: '38938-7',
  display: 'VA Compensation and Pension (C and P) examination brain/spinal cord',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationChronicFatigueSyndrome: Coding = {
  code: '38939-5',
  display: 'VA Compensation and Pension (C and P) examination chronic fatigue syndrome',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationColdInjuryProtocol: Coding = {
  code: '38940-3',
  display: 'VA Compensation and Pension (C and P) examination cold injury protocol',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationCranialNerves: Coding = {
  code: '38941-1',
  display: 'VA Compensation and Pension (C and P) examination cranial nerves',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationCushingsSyndrome: Coding = {
  code: '38942-9',
  display: 'VA Compensation and Pension (C and P) examination Cushings syndrome',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationDentalOral: Coding = {
  code: '38943-7',
  display: 'VA Compensation and Pension (C and P) examination dental/oral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationDiabetesMellitus: Coding = {
  code: '38944-5',
  display: 'VA Compensation and Pension (C and P) examination diabetes mellitus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationMiscellaneousDigestiveConditions: Coding = {
  code: '38945-2',
  display: 'VA Compensation and Pension (C and P) examination miscellaneous digestive conditions',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationEarDisease: Coding = {
  code: '38946-0',
  display: 'VA Compensation and Pension (C and P) examination ear disease',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationMentalHealthEatingDisorders: Coding = {
  code: '38947-8',
  display: 'VA Compensation and Pension (C and P) examination mental health eating disorders',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationMiscellaneousEndocrineDiseases: Coding = {
  code: '38948-6',
  display: 'VA Compensation and Pension (C and P) examination miscellaneous endocrine diseases',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationEpilepsyNarcolepsy: Coding = {
  code: '38949-4',
  display: 'VA Compensation and Pension (C and P) examination epilepsy/narcolepsy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationEsophagusHiatalHernia: Coding = {
  code: '38950-2',
  display: 'VA Compensation and Pension (C and P) examination esophagus/hiatal hernia',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationEye: Coding = {
  code: '38951-0',
  display: 'VA Compensation and Pension (C and P) examination eye',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationFeet: Coding = {
  code: '38952-8',
  display: 'VA Compensation and Pension (C and P) examination feet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationFibromyalgia: Coding = {
  code: '38953-6',
  display: 'VA Compensation and Pension (C and P) examination fibromyalgia',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationGeneralMedical: Coding = {
  code: '38954-4',
  display: 'VA Compensation and Pension (C and P) examination general medical',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationGenitourinary: Coding = {
  code: '38955-1',
  display: 'VA Compensation and Pension (C and P) examination genitourinary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationDisabilityInGulfWarVeterans: Coding = {
  code: '38956-9',
  display: 'VA Compensation and Pension (C and P) examination disability in gulf war veterans',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationGynecologicalConditionsDisordersOfTheBreast: Coding = {
  code: '38957-7',
  display: 'VA Compensation and Pension (C and P) examination gynecological conditions/disorders of the breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationHandThumbFingers: Coding = {
  code: '38958-5',
  display: 'VA Compensation and Pension (C and P) examination hand/thumb/fingers',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationHeart: Coding = {
  code: '38959-3',
  display: 'VA Compensation and Pension (C and P) examination heart',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationHemicDisorders: Coding = {
  code: '38960-1',
  display: 'VA Compensation and Pension (C and P) examination hemic disorders',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationHIVRelatedIllness: Coding = {
  code: '38961-9',
  display: 'VA Compensation and Pension (C and P) examination HIV-related illness',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationHypertension: Coding = {
  code: '38962-7',
  display: 'VA Compensation and Pension (C and P) examination hypertension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationInfectiousImmuneNutritionalDisabilities: Coding = {
  code: '38963-5',
  display: 'VA Compensation and Pension (C and P) examination infectious/immune/nutritional disabilities',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationInitialEvaluationPostTraumaticStressDisorder: Coding = {
  code: '38964-3',
  display: 'VA Compensation and Pension (C and P) examination initial evaluation post-traumatic stress disorder',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationLargeSmallIntestines: Coding = {
  code: '38965-0',
  display: 'VA Compensation and Pension (C and P) examination large/small intestines',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationExtremityJoints: Coding = {
  code: '38966-8',
  display: 'VA Compensation and Pension (C and P) examination extremity joints',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationLiverGallBladderPancreas: Coding = {
  code: '38967-6',
  display: 'VA Compensation and Pension (C and P) examination liver/gall bladder/pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationLymphaticDisorders: Coding = {
  code: '38968-4',
  display: 'VA Compensation and Pension (C and P) examination lymphatic disorders',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationGeneralMentalDisorders: Coding = {
  code: '38969-2',
  display: 'VA Compensation and Pension (C and P) examination general mental disorders',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationMouthLipsTongue: Coding = {
  code: '38970-0',
  display: 'VA Compensation and Pension (C and P) examination mouth/lips/tongue',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationMuscles: Coding = {
  code: '38971-8',
  display: 'VA Compensation and Pension (C and P) examination muscles',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationMiscellaneousNeurologicalDisorders: Coding = {
  code: '38972-6',
  display: 'VA Compensation and Pension (C and P) examination miscellaneous neurological disorders',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationNoseSinusLarynxPharynx: Coding = {
  code: '38973-4',
  display: 'VA Compensation and Pension (C and P) examination nose/sinus/larynx/pharynx',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationPeripheralNerves: Coding = {
  code: '38974-2',
  display: 'VA Compensation and Pension (C and P) examination peripheral nerves',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationPrisonerOfWarProtocol: Coding = {
  code: '38975-9',
  display: 'VA Compensation and Pension (C and P) examination prisoner of war protocol',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationPulmonaryTuberculosisMycobacterialDiseases: Coding = {
  code: '38976-7',
  display: 'VA Compensation and Pension (C and P) examination pulmonary tuberculosis/mycobacterial diseases',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationRectumAnus: Coding = {
  code: '38977-5',
  display: 'VA Compensation and Pension (C and P) examination rectum/anus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationResidualsOfAmputations: Coding = {
  code: '38978-3',
  display: 'VA Compensation and Pension (C and P) examination residuals of amputations',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationObstructiveRestrictiveInterstitialRespiratoryDiseases: Coding = {
  code: '38979-1',
  display: 'VA Compensation and Pension (C and P) examination obstructive/restrictive/interstitial respiratory diseases',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationMiscellaneousRespiratoryDiseases: Coding = {
  code: '38980-9',
  display: 'VA Compensation and Pension (C and P) examination miscellaneous respiratory diseases',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationReviewEvaluationPostTraumaticStressDisorder: Coding = {
  code: '38981-7',
  display: 'VA Compensation and Pension (C and P) examination review evaluation post-traumatic stress disorder',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationScars: Coding = {
  code: '38982-5',
  display: 'VA Compensation and Pension (C and P) examination scars',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationSenseOfSmellTaste: Coding = {
  code: '38983-3',
  display: 'VA Compensation and Pension (C and P) examination sense of smell/taste',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationSkinDiseasesOtherThanScars: Coding = {
  code: '38984-1',
  display: 'VA Compensation and Pension (C and P) examination skin diseases other than scars',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationSocialIndustrialSurvey: Coding = {
  code: '38985-8',
  display: 'VA Compensation and Pension (C and P) examination social/industrial survey',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationSpine: Coding = {
  code: '38986-6',
  display: 'VA Compensation and Pension (C and P) examination spine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationStomachDuodenumPeritonealAdhesions: Coding = {
  code: '38987-4',
  display: 'VA Compensation and Pension (C and P) examination stomach/duodenum/peritoneal adhesions',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VACompensationAndPensionCAndPExaminationThyroidParathyroidDiseases: Coding = {
  code: '38988-2',
  display: 'VA Compensation and Pension (C and P) examination thyroid/parathyroid diseases',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleLocalizationOfUnspecifiedBodyRegion: Coding = {
  code: '39026-0',
  display: 'CT Guidance for needle localization of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForNeedleLocalizationOfUnspecifiedBodyRegion: Coding = {
  code: '39027-8',
  display: 'Fluoroscopy Guidance for needle localization of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceForNeedleLocalizationOfUnspecifiedBodyRegion: Coding = {
  code: '39028-6',
  display: 'MRI Guidance for needle localization of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitAndFaceMRIWAndWOContrastIV: Coding = {
  code: '39029-4',
  display: 'Orbit and Face MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinBilateralUS: Coding = {
  code: '39030-2',
  display: 'Vein - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteryBilateralUSDoppler: Coding = {
  code: '39031-0',
  display: 'Extremity artery - bilateral US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyTransplantUS: Coding = {
  code: '39032-8',
  display: 'Kidney transplant US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityMRIWOContrast: Coding = {
  code: '39033-6',
  display: 'Upper extremity MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityMRIWAndWOContrastIV: Coding = {
  code: '39034-4',
  display: 'Upper extremity MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinUS: Coding = {
  code: '39036-9',
  display: 'Vein US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityMRIWContrastIV: Coding = {
  code: '39037-7',
  display: 'Upper extremity MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitAndFaceMRIWContrastIV: Coding = {
  code: '39038-5',
  display: 'Orbit and Face MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrachiocephalicArteryUSDoppler: Coding = {
  code: '39039-3',
  display: 'Brachiocephalic artery US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AVFistulaUS: Coding = {
  code: '39040-1',
  display: 'AV fistula US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteryUSDoppler: Coding = {
  code: '39042-7',
  display: 'Extremity artery US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionMRIAnd3DReconstruction: Coding = {
  code: '39043-5',
  display: 'Unspecified body region MRI and 3D reconstruction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVesselsUSDopplerLimited: Coding = {
  code: '39044-3',
  display: 'Head vessels US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinUSLimited: Coding = {
  code: '39045-0',
  display: 'Vein US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisCTLimitedPelvimetryWOContrast: Coding = {
  code: '39046-8',
  display: 'Pelvis CT limited pelvimetry WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipFluoroscopyDuringSurgery: Coding = {
  code: '39047-6',
  display: 'Hip Fluoroscopy during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaXRayAPSingleView: Coding = {
  code: '39048-4',
  display: 'Scapula X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayAPSingleView: Coding = {
  code: '39049-2',
  display: 'Spine Thoracic and Lumbar X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsXRayAPSingleView: Coding = {
  code: '39050-0',
  display: 'Ribs X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayLateral: Coding = {
  code: '39051-8',
  display: 'Chest X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRayLateral: Coding = {
  code: '39052-6',
  display: 'Spine X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsXRayLateral: Coding = {
  code: '39053-4',
  display: 'Ribs X-ray lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastDuctMammogramWContrastIntraDuct: Coding = {
  code: '39054-2',
  display: 'Breast duct Mammogram W contrast intra duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinsFluoroscopicAngiogramWContrastIV: Coding = {
  code: '39055-9',
  display: 'Extremity veins Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionXRayWManualStress: Coding = {
  code: '39056-7',
  display: 'Unspecified body region X-ray W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '39057-5',
  display: 'Pulmonary artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SalivaryGlandXRay: Coding = {
  code: '39058-3',
  display: 'Salivary gland X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineUpperFluoroscopyWAirAndBariumContrastPO: Coding = {
  code: '39059-1',
  display: 'Gastrointestine upper Fluoroscopy W air and barium contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsXRay2Views: Coding = {
  code: '39060-9',
  display: 'Ribs X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumAndCoccyxXRay3Views: Coding = {
  code: '39061-7',
  display: 'Sacrum and Coccyx X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsXRay3Views: Coding = {
  code: '39062-5',
  display: 'Ribs X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRay5ViewsWFlexionAndWExtension: Coding = {
  code: '39063-3',
  display: 'Spine Lumbar X-ray 5 views W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsXRayAnteriorAndLateral: Coding = {
  code: '39064-1',
  display: 'Ribs X-ray anterior and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayAPAndInletAndOutletAndOblique: Coding = {
  code: '39065-8',
  display: 'Pelvis X-ray AP and inlet and outlet and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestFluoroscopyAPAndLateral: Coding = {
  code: '39066-6',
  display: 'Chest Fluoroscopy AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndThoracicAndLumbarXRayAPAndLateral: Coding = {
  code: '39067-4',
  display: 'Spine Cervical and Thoracic and Lumbar X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayAPAndLateralStanding: Coding = {
  code: '39068-2',
  display: 'Foot X-ray AP and lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayAPAndLateral: Coding = {
  code: '39069-0',
  display: 'Foot X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPAndLateralAndLordotic: Coding = {
  code: '39070-8',
  display: 'Chest X-ray AP and lateral and lordotic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayAPAndLateralAndMerchants: Coding = {
  code: '39071-6',
  display: 'Knee X-ray AP and lateral and Merchants',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRayAPAndLateralAndOblique: Coding = {
  code: '39072-4',
  display: 'Ankle X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayAPAndLateralAndRightObliqueAndLeftOblique: Coding = {
  code: '39073-2',
  display: 'Knee X-ray AP and lateral and right oblique and left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPAndLateralAndRightObliqueAndLeftOblique: Coding = {
  code: '39074-0',
  display: 'Chest X-ray AP and lateral and right oblique and left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesXRayAPAndOblique: Coding = {
  code: '39075-7',
  display: 'Toes X-ray AP and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayAPAndOblique: Coding = {
  code: '39076-5',
  display: 'Foot X-ray AP and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayAPAndTransthoracic: Coding = {
  code: '39077-3',
  display: 'Shoulder X-ray AP and transthoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerXRayPAAndLateralAndOblique: Coding = {
  code: '39078-1',
  display: 'Finger X-ray PA and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRayPAAndOblique: Coding = {
  code: '39079-9',
  display: 'Hand X-ray PA and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HepaticVeinsFluoroscopicAngiogramWContrastIV: Coding = {
  code: '39093-0',
  display: 'Hepatic veins Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryCervicalFluoroscopicAngiogramWContrastIA: Coding = {
  code: '39094-8',
  display: 'Carotid artery.cervical Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryAndCerebralArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '39095-5',
  display: 'Carotid artery and Cerebral artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HepaticVeinsFluoroscopicAngiogramWContrastIVAndWHemodynamics: Coding = {
  code: '39096-3',
  display: 'Hepatic veins Fluoroscopic angiogram W contrast IV and W hemodynamics',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryBilateralAndCerebralArteryBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '39097-1',
  display: 'Carotid artery - bilateral and Cerebral artery - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryCervicalBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '39098-9',
  display: 'Carotid artery.cervical - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsBilateralAndChestXRay4ViewsAndPAChest: Coding = {
  code: '39099-7',
  display: 'Ribs - bilateral and Chest X-ray 4 views and PA chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsRightAndChestXRayLateralAndPAChest: Coding = {
  code: '39100-3',
  display: 'Ribs - right and Chest X-ray lateral and PA chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsAndChestXRayLateralAndPAChest: Coding = {
  code: '39101-1',
  display: 'Ribs and Chest X-ray lateral and PA chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForVascularAccessOfVessel: Coding = {
  code: '39138-3',
  display: 'Fluoroscopic angiogram Guidance for vascular access of Vessel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForVascularAccessOfUnspecifiedBodyRegion: Coding = {
  code: '39139-1',
  display: 'US Guidance for vascular access of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartMRICineForBloodFlowVelocityMapping: Coding = {
  code: '39140-9',
  display: 'Heart MRI cine for blood flow velocity mapping',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneMarrowMRIForBloodFlow: Coding = {
  code: '39141-7',
  display: 'Bone marrow MRI for blood flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCTPerfusionWContrastIV: Coding = {
  code: '39142-5',
  display: 'Head CT perfusion W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineUpperFluoroscopyWAirContrastPO: Coding = {
  code: '39144-1',
  display: 'Gastrointestine upper Fluoroscopy W air contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastDuctLeftMammogramWContrastIntraMultipleDucts: Coding = {
  code: '39145-8',
  display: 'Breast duct - left Mammogram W contrast intra multiple ducts',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastDuctBilateralMammogramWContrastIntraMultipleDucts: Coding = {
  code: '39146-6',
  display: 'Breast duct - bilateral Mammogram W contrast intra multiple ducts',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastDuctRightMammogramWContrastIntraMultipleDucts: Coding = {
  code: '39147-4',
  display: 'Breast duct - right Mammogram W contrast intra multiple ducts',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastDuctMammogramWContrastIntraMultipleDucts: Coding = {
  code: '39148-2',
  display: 'Breast duct Mammogram W contrast intra multiple ducts',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestinalSystemAndRespiratorySystemXRayForForeignBody: Coding = {
  code: '39149-0',
  display: 'Gastrointestinal system and Respiratory system X-ray for foreign body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastFFDMammogramPostLocalization: Coding = {
  code: '39150-8',
  display: 'Breast FFD mammogram Post Localization',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VasDeferensFluoroscopyWContrastIntraVasDeferens: Coding = {
  code: '39151-6',
  display: 'Vas deferens Fluoroscopy W contrast intra vas deferens',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastFFDMammogramDiagnostic: Coding = {
  code: '39152-4',
  display: 'Breast FFD mammogram diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastFFDMammogramScreening: Coding = {
  code: '39153-2',
  display: 'Breast FFD mammogram screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralFFDMammogramDiagnostic: Coding = {
  code: '39154-0',
  display: 'Breast - bilateral FFD mammogram diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityMRIWAndWOContrastIV: Coding = {
  code: '39291-0',
  display: 'Lower extremity MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityMRIWOContrast: Coding = {
  code: '39292-8',
  display: 'Lower extremity MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityMRIWContrastIV: Coding = {
  code: '39293-6',
  display: 'Lower extremity MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayAPWInternalRotationAndWExternalRotationAndAxillary: Coding = {
  code: '39321-5',
  display: 'Shoulder X-ray AP (W internal rotation and W external rotation) and axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCTWContrastIntradisc: Coding = {
  code: '39322-3',
  display: 'Spine CT W contrast intradisc',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayRightPosteriorOblique: Coding = {
  code: '39323-1',
  display: 'Abdomen X-ray right posterior oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayPAWClenchedFist: Coding = {
  code: '39324-9',
  display: 'Wrist - left X-ray PA W clenched fist',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPWInternalRotationAndGrasheyAndAxillaryAndOutlet: Coding = {
  code: '39325-6',
  display: 'Shoulder - left X-ray AP (W internal rotation) and Grashey and axillary and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLeftAndChestXRay: Coding = {
  code: '39326-4',
  display: 'Ribs - left and Chest X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenAndFetusXRayForFetalAge: Coding = {
  code: '39327-2',
  display: 'Abdomen and Fetus X-ray for fetal age',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPWInternalRotationAndWExternalRotation: Coding = {
  code: '39328-0',
  display: 'Shoulder - left X-ray AP (W internal rotation and W external rotation)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAPWInternalRotationAndWExternalRotation: Coding = {
  code: '39329-8',
  display: 'Shoulder - bilateral X-ray AP (W internal rotation and W external rotation)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRayAPAndLateralStanding: Coding = {
  code: '39330-6',
  display: 'Ankle - bilateral X-ray AP and lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralXRayAPAndLateralStanding: Coding = {
  code: '39331-4',
  display: 'Foot - bilateral X-ray AP and lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRayAPAndLateralStanding: Coding = {
  code: '39332-2',
  display: 'Foot - left X-ray AP and lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPAndLateralStanding: Coding = {
  code: '39333-0',
  display: 'Spine Lumbar X-ray AP and lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRayAPAndLateralAndObliqueStanding: Coding = {
  code: '39334-8',
  display: 'Foot - left X-ray AP and lateral and oblique standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPWInternalRotationAndWExternalRotationAndAxillary: Coding = {
  code: '39335-5',
  display: 'Shoulder - left X-ray AP (W internal rotation and W external rotation) and axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAPWInternalRotationAndWExternalRotationAndAxillary: Coding = {
  code: '39336-3',
  display: 'Shoulder - bilateral X-ray AP (W internal rotation and W external rotation) and axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAPWInternalRotationAndWExternalRotationAndAxillaryAndOutlet: Coding = {
  code: '39337-1',
  display: 'Shoulder - bilateral X-ray AP (W internal rotation and W external rotation) and axillary and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPWInternalRotationAndWExternalRotationAndAxillaryAndY: Coding = {
  code: '39338-9',
  display: 'Shoulder - left X-ray AP (W internal rotation and W external rotation) and axillary and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAPAndAxillaryAndOutletAnd30DegreeCaudalAngle: Coding = {
  code: '39339-7',
  display: 'Shoulder - bilateral X-ray AP and axillary and outlet and 30 degree caudal angle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayLateralStandingAndWFlexionAndWExtension: Coding = {
  code: '39340-5',
  display: 'Spine Lumbar X-ray lateral standing and W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayLateralAndPAWInspirationAndExpiration: Coding = {
  code: '39341-3',
  display: 'Chest X-ray lateral and PA W inspiration and expiration',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAPWInternalRotationAndWExternalRotationAndY: Coding = {
  code: '39343-9',
  display: 'Shoulder - bilateral X-ray AP (W internal rotation and W external rotation) and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAPWInternalRotationAndWExternalRotationAndAxillaryAndY: Coding = {
  code: '39344-7',
  display: 'Shoulder - bilateral X-ray AP (W internal rotation and W external rotation) and axillary and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRaySunriseAndTunnelStanding: Coding = {
  code: '39345-4',
  display: 'Knee - left X-ray Sunrise and tunnel standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAPWInternalRotationAndWestPoint: Coding = {
  code: '39346-2',
  display: 'Shoulder - bilateral X-ray AP (W internal rotation) and West Point',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPWInternalRotationAndWestPoint: Coding = {
  code: '39347-0',
  display: 'Shoulder - left X-ray AP (W internal rotation) and West Point',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPWInternalRotationAndWExternalRotationAndY: Coding = {
  code: '39348-8',
  display: 'Shoulder - left X-ray AP (W internal rotation and W external rotation) and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralFluoroscopyWContrastRetrograde: Coding = {
  code: '39349-6',
  display: 'Kidney - bilateral Fluoroscopy W contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayGrasheyAndOutletAndSerendipity: Coding = {
  code: '39350-4',
  display: 'Shoulder - bilateral X-ray Grashey and outlet and Serendipity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsUpperAnteriorAndPosteriorLeftXRay: Coding = {
  code: '39351-2',
  display: 'Ribs upper anterior and posterior - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsPosteriorBilateralXRay: Coding = {
  code: '39352-0',
  display: 'Ribs posterior - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsUpperPosteriorLeftXRay: Coding = {
  code: '39353-8',
  display: 'Ribs upper posterior - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralXRayTomographWOContrast: Coding = {
  code: '39359-5',
  display: 'Kidney - bilateral X-ray tomograph WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayAndInletAndOutlet: Coding = {
  code: '39360-3',
  display: 'Pelvis X-ray and inlet and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForAbscessDrainageOfLiver: Coding = {
  code: '39361-1',
  display: 'Fluoroscopy Guidance for abscess drainage of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPlacementOfTubeInChest: Coding = {
  code: '39362-9',
  display: 'Fluoroscopy Guidance for placement of tube in Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FistulaFluoroscopyWContrastRetrograde: Coding = {
  code: '39363-7',
  display: 'Fistula Fluoroscopy W contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRay3ViewsAndRadialDeviation: Coding = {
  code: '39364-5',
  display: 'Wrist - right X-ray 3 views and radial deviation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRay3ViewsAndUlnarDeviation: Coding = {
  code: '39365-2',
  display: 'Wrist - right X-ray 3 views and ulnar deviation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaXRayLateralAndOutlet: Coding = {
  code: '39366-0',
  display: 'Scapula X-ray lateral and outlet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPAndLateralStanding: Coding = {
  code: '39367-8',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis AP and lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayAPAndLateralStanding: Coding = {
  code: '39368-6',
  display: 'Ankle - right X-ray AP and lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayAPAndLateralAndObliqueWManualStress: Coding = {
  code: '39369-4',
  display: 'Ankle - right X-ray AP and lateral and oblique W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayAndViewWManualStress: Coding = {
  code: '39370-2',
  display: 'Ankle - right X-ray and (view W manual stress)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayAPAndLateralAndObliqueStanding: Coding = {
  code: '39371-0',
  display: 'Ankle - right X-ray AP and lateral and oblique standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayAndMortise: Coding = {
  code: '39372-8',
  display: 'Ankle - right X-ray and Mortise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRayAndOblique: Coding = {
  code: '39373-6',
  display: 'Elbow - right X-ray and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRayAPAndLateralStanding: Coding = {
  code: '39374-4',
  display: 'Foot - right X-ray AP and lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRayAPAndLateralAndObliqueStanding: Coding = {
  code: '39375-1',
  display: 'Foot - right X-ray AP and lateral and oblique standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusRightAndUlnaRightXRayAndOblique: Coding = {
  code: '39376-9',
  display: 'Radius - right and Ulna - right X-ray and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayAndLateralCrosstable: Coding = {
  code: '39377-7',
  display: 'Hip - right X-ray and lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay2ViewsAndOblique: Coding = {
  code: '39378-5',
  display: 'Knee - right X-ray 2 views and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay2ViewsAndSunrise: Coding = {
  code: '39379-3',
  display: 'Knee - right X-ray 2 views and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay2ViewsAndSunriseAndTunnel: Coding = {
  code: '39380-1',
  display: 'Knee - right X-ray 2 views and Sunrise and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay2ViewsAndTunnel: Coding = {
  code: '39381-9',
  display: 'Knee - right X-ray 2 views and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay2ViewsAndTunnelStanding: Coding = {
  code: '39382-7',
  display: 'Knee - right X-ray 2 views and tunnel standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay3ViewsAndSunrise: Coding = {
  code: '39383-5',
  display: 'Knee - right X-ray 3 views and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay4ViewsAndAPStanding: Coding = {
  code: '39384-3',
  display: 'Knee - right X-ray 4 views and AP standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay4ViewsAndOblique: Coding = {
  code: '39385-0',
  display: 'Knee - right X-ray 4 views and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay4ViewsAndTunnel: Coding = {
  code: '39386-8',
  display: 'Knee - right X-ray 4 views and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay4ViewsAndSunriseAndTunnel: Coding = {
  code: '39387-6',
  display: 'Knee - right X-ray 4 views and Sunrise and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAPAndLateralAndRightObliqueAndLeftOblique: Coding = {
  code: '39388-4',
  display: 'Knee - right X-ray AP and lateral and right oblique and left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAndTunnel: Coding = {
  code: '39389-2',
  display: 'Knee - right X-ray and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAndOblique: Coding = {
  code: '39390-0',
  display: 'Knee - right X-ray and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAndSunrise: Coding = {
  code: '39391-8',
  display: 'Knee - right X-ray and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayWInternalRotationAndWExternalRotationAndAxillary: Coding = {
  code: '39392-6',
  display: 'Shoulder - right X-ray (W internal rotation and W external rotation) and axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRay3ViewsAndAxillary: Coding = {
  code: '39393-4',
  display: 'Shoulder - right X-ray 3 views and axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRay3ViewsAndY: Coding = {
  code: '39394-2',
  display: 'Shoulder - right X-ray 3 views and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAPWInternalRotationAndWExternalRotation: Coding = {
  code: '39395-9',
  display: 'Shoulder - right X-ray AP (W internal rotation and W external rotation)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAPWInternalRotationAndWestPoint: Coding = {
  code: '39396-7',
  display: 'Shoulder - right X-ray AP (W internal rotation) and West Point',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAPWInternalRotationAndWExternalRotationAndWestPoint: Coding = {
  code: '39397-5',
  display: 'Shoulder - right X-ray AP (W internal rotation and W external rotation) and West Point',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaRightAndFibulaRightXRayAndOblique: Coding = {
  code: '39398-3',
  display: 'Tibia - right and Fibula - right X-ray and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRay3ViewsAndCarpalTunnel: Coding = {
  code: '39399-1',
  display: 'Wrist - right X-ray 3 views and carpal tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayAndCarpalTunnel: Coding = {
  code: '39400-7',
  display: 'Wrist - right X-ray and carpal tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayAPAndGrasheyAndAxillary: Coding = {
  code: '39401-5',
  display: 'Shoulder X-ray AP and Grashey and axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayAPWInternalRotationAndWExternalRotation: Coding = {
  code: '39402-3',
  display: 'Shoulder X-ray AP (W internal rotation and W external rotation)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayAxillaryAndTranscapular: Coding = {
  code: '39403-1',
  display: 'Shoulder X-ray axillary and transcapular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRay3ViewsAndSubmentovertex: Coding = {
  code: '39404-9',
  display: 'Sinuses X-ray 3 views and submentovertex',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternumXRayLateralAndRightObliqueAndLeftOblique: Coding = {
  code: '39405-6',
  display: 'Sternum X-ray lateral and right oblique and left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternumXRayLateralAndRightAnteriorOblique: Coding = {
  code: '39406-4',
  display: 'Sternum X-ray lateral and right anterior oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRay5ViewsAndOblique: Coding = {
  code: '39407-2',
  display: 'Spine Thoracic X-ray 5 views and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayTomographAP: Coding = {
  code: '39408-0',
  display: 'Spine Thoracic X-ray tomograph AP',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayTomographLateral: Coding = {
  code: '39409-8',
  display: 'Spine Thoracic X-ray tomograph lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayAPSingleViewWLeftBending: Coding = {
  code: '39410-6',
  display: 'Spine Thoracic X-ray AP single view W left bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayAPSingleViewWRightBending: Coding = {
  code: '39411-4',
  display: 'Spine Thoracic X-ray AP single view W right bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayAndSwimmers: Coding = {
  code: '39412-2',
  display: 'Spine Thoracic X-ray and Swimmers',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRay4ViewsAndOblique: Coding = {
  code: '39413-0',
  display: 'Spine Thoracic X-ray 4 views and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayAndOblique: Coding = {
  code: '39414-8',
  display: 'Spine Thoracic X-ray and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineUS: Coding = {
  code: '39415-5',
  display: 'Gastrointestine US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GenitourinarySystemUS: Coding = {
  code: '39416-3',
  display: 'Genitourinary system US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinBilateralUSDoppler: Coding = {
  code: '39418-9',
  display: 'Extremity vein - bilateral US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsBilateralUSDoppler: Coding = {
  code: '39419-7',
  display: 'Renal vessels - bilateral US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinBilateralUSDoppler: Coding = {
  code: '39420-5',
  display: 'Lower extremity vein - bilateral US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityArteryBilateralUSDoppler: Coding = {
  code: '39421-3',
  display: 'Lower extremity artery - bilateral US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsBilateralUSDoppler: Coding = {
  code: '39422-1',
  display: 'Lower extremity vessels - bilateral US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteryBilateralUSDoppler: Coding = {
  code: '39423-9',
  display: 'Upper extremity artery - bilateral US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVesselsUSDopplerLimited: Coding = {
  code: '39424-7',
  display: 'Extremity vessels US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacArteryUSDoppler: Coding = {
  code: '39425-4',
  display: 'Iliac artery US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsUSDoppler: Coding = {
  code: '39426-2',
  display: 'Renal vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryLeftUSDoppler: Coding = {
  code: '39427-0',
  display: 'Carotid artery - left US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteryLeftUSDoppler: Coding = {
  code: '39428-8',
  display: 'Extremity artery - left US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinLeftUSDoppler: Coding = {
  code: '39429-6',
  display: 'Extremity vein - left US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsLeftUSDopplerLimited: Coding = {
  code: '39430-4',
  display: 'Lower extremity vessels - left US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsLeftUSDoppler: Coding = {
  code: '39431-2',
  display: 'Lower extremity vessels - left US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinLeftUSDoppler: Coding = {
  code: '39432-0',
  display: 'Lower extremity vein - left US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsLeftUSDoppler: Coding = {
  code: '39433-8',
  display: 'Upper extremity vessels - left US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityArteryUSDoppler: Coding = {
  code: '39434-6',
  display: 'Lower extremity artery US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalArteryUSDoppler: Coding = {
  code: '39435-3',
  display: 'Renal artery US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsUSDopplerLimited: Coding = {
  code: '39436-1',
  display: 'Renal vessels US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryRightUSDoppler: Coding = {
  code: '39437-9',
  display: 'Carotid artery - right US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteryRightUSDoppler: Coding = {
  code: '39439-5',
  display: 'Extremity artery - right US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinRightUSDoppler: Coding = {
  code: '39440-3',
  display: 'Extremity vein - right US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsRightUSDopplerLimited: Coding = {
  code: '39441-1',
  display: 'Lower extremity vessels - right US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsRightUSDoppler: Coding = {
  code: '39442-9',
  display: 'Lower extremity vessels - right US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinRightUSDoppler: Coding = {
  code: '39443-7',
  display: 'Lower extremity vein - right US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsRightUSDoppler: Coding = {
  code: '39444-5',
  display: 'Upper extremity vessels - right US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VesselsUSDoppler: Coding = {
  code: '39445-2',
  display: 'Vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TesticleVesselsUSDoppler: Coding = {
  code: '39446-0',
  display: 'Testicle vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteryUSDoppler: Coding = {
  code: '39447-8',
  display: 'Upper extremity artery US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsUSDoppler: Coding = {
  code: '39448-6',
  display: 'Upper extremity vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinUSDoppler: Coding = {
  code: '39449-4',
  display: 'Extremity vein US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineUSWContrastPO: Coding = {
  code: '39450-2',
  display: 'Gastrointestine US W contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAbscessDrainageOfUnspecifiedBodyRegion: Coding = {
  code: '39451-0',
  display: 'US Guidance for abscess drainage of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfOvary: Coding = {
  code: '39452-8',
  display: 'US Guidance for aspiration of Ovary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TendonUS: Coding = {
  code: '39453-6',
  display: 'Tendon US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverTransplantUS: Coding = {
  code: '39454-4',
  display: 'Liver transplant US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLowerPosteriorXRay: Coding = {
  code: '39489-0',
  display: 'Ribs lower posterior X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightAndTibiaRightXRayForLegLength: Coding = {
  code: '39490-8',
  display: 'Femur - right and Tibia - right X-ray for leg length',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsUpperAnteriorAndPosteriorRightXRay: Coding = {
  code: '39491-6',
  display: 'Ribs upper anterior and posterior - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsUpperPosteriorRightXRay: Coding = {
  code: '39492-4',
  display: 'Ribs upper posterior - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLowerPosteriorRightXRay: Coding = {
  code: '39493-2',
  display: 'Ribs lower posterior - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalWallUS: Coding = {
  code: '39494-0',
  display: 'Abdominal wall US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVesselsBilateralUSDoppler: Coding = {
  code: '39495-7',
  display: 'Extremity vessels - bilateral US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinBilateralUSDoppler: Coding = {
  code: '39496-5',
  display: 'Upper extremity vein - bilateral US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacVesselsUSDoppler: Coding = {
  code: '39497-3',
  display: 'Iliac vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralVesselsLeftUSDoppler: Coding = {
  code: '39498-1',
  display: 'Femoral vessels - left US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityArteryLeftUSDoppler: Coding = {
  code: '39499-9',
  display: 'Lower extremity artery - left US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteryLeftUSDoppler: Coding = {
  code: '39500-4',
  display: 'Upper extremity artery - left US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinLeftUSDoppler: Coding = {
  code: '39501-2',
  display: 'Upper extremity vein - left US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OvarianVesselsUSDoppler: Coding = {
  code: '39502-0',
  display: 'Ovarian vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVesselsRightUSDoppler: Coding = {
  code: '39503-8',
  display: 'Extremity vessels - right US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralVesselsRightUSDoppler: Coding = {
  code: '39504-6',
  display: 'Femoral vessels - right US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityArteryRightUSDoppler: Coding = {
  code: '39505-3',
  display: 'Lower extremity artery - right US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteryRightUSDoppler: Coding = {
  code: '39506-1',
  display: 'Upper extremity artery - right US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinRightUSDoppler: Coding = {
  code: '39507-9',
  display: 'Upper extremity vein - right US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UmbilicalVesselsUSDoppler: Coding = {
  code: '39508-7',
  display: 'Umbilical vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PancreasTransplantUS: Coding = {
  code: '39509-5',
  display: 'Pancreas transplant US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LymphaticsPelvicFluoroscopyWContrastIntraLymphatic: Coding = {
  code: '39510-3',
  display: 'Lymphatics pelvic Fluoroscopy W contrast intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayAndOblique: Coding = {
  code: '39511-1',
  display: 'Pelvis X-ray and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayAPAndDaneliusMiller: Coding = {
  code: '39512-9',
  display: 'Hip - right X-ray AP and Danelius Miller',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayAndDaneliusMiller: Coding = {
  code: '39513-7',
  display: 'Hip - right X-ray and Danelius Miller',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayDaneliusMiller: Coding = {
  code: '39514-5',
  display: 'Hip - right X-ray Danelius Miller',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayLateralWFlexionAndWExtension: Coding = {
  code: '39515-2',
  display: 'Wrist - right X-ray lateral W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayStrykerNotch: Coding = {
  code: '39516-0',
  display: 'Shoulder X-ray Stryker Notch',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayStrykerNotchAndWestPoint: Coding = {
  code: '39517-8',
  display: 'Shoulder X-ray Stryker Notch and West Point',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BonesLongXRaySurveyLimited: Coding = {
  code: '39518-6',
  display: 'Bones long X-ray survey limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayPAAndRightLateralAndLeftLateral: Coding = {
  code: '39519-4',
  display: 'Skull X-ray PA and right lateral and left lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayPAAndRightLateralAndLeftLateralAndTowne: Coding = {
  code: '39520-2',
  display: 'Skull X-ray PA and right lateral and left lateral and Towne',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayPAAndRightLateralAndLeftLateralAndCaldwellAndTowne: Coding = {
  code: '39521-0',
  display: 'Skull X-ray PA and right lateral and left lateral and Caldwell and Towne',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfLymphNode: Coding = {
  code: '39522-8',
  display: 'US Guidance for biopsy of Lymph node',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ArteryUSDoppler: Coding = {
  code: '39523-6',
  display: 'Artery US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinUSDopplerLimited: Coding = {
  code: '39524-4',
  display: 'Vein US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinUSDoppler: Coding = {
  code: '39525-1',
  display: 'Vein US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityUSLimited: Coding = {
  code: '39526-9',
  display: 'Extremity US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionUSOfForeignBody: Coding = {
  code: '39527-7',
  display: 'Unspecified body region US of foreign body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonarySystemScan: Coding = {
  code: '39619-2',
  display: 'Pulmonary system Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanGuidanceForAbscessLocalizationLimited: Coding = {
  code: '39620-0',
  display: 'Scan Guidance for abscess localization limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTGuidanceForAbscessLocalization: Coding = {
  code: '39621-8',
  display: 'SPECT Guidance for abscess localization',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTGuidanceForAbscessLocalizationWholeBody: Coding = {
  code: '39622-6',
  display: 'SPECT Guidance for abscess localization whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanGuidanceForAbscessLocalizationWholeBody: Coding = {
  code: '39623-4',
  display: 'Scan Guidance for abscess localization whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalGlandScanWI131NP59IV: Coding = {
  code: '39624-2',
  display: 'Adrenal gland Scan W I-131 NP59 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ArteryScanWTc99mDTPAIA: Coding = {
  code: '39625-9',
  display: 'Artery Scan W Tc-99m DTPA IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinBilateralScan: Coding = {
  code: '39626-7',
  display: 'Vein - bilateral Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanLimited: Coding = {
  code: '39627-5',
  display: 'Bone Scan limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MeckelsDiverticulumSPECT: Coding = {
  code: '39628-3',
  display: 'Meckels diverticulum SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MeckelsDiverticulumScan: Coding = {
  code: '39629-1',
  display: 'Meckels diverticulum Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanWTc99mHMPAOIV: Coding = {
  code: '39630-9',
  display: 'Brain Scan W Tc-99m HMPAO IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainSPECTWTc99mHMPAOIV: Coding = {
  code: '39631-7',
  display: 'Brain SPECT W Tc-99m HMPAO IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainSPECT: Coding = {
  code: '39632-5',
  display: 'Brain SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanStatic: Coding = {
  code: '39633-3',
  display: 'Brain Scan static',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanStaticLimited: Coding = {
  code: '39634-1',
  display: 'Brain Scan static limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanWTl201IV: Coding = {
  code: '39635-8',
  display: 'Brain Scan W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanFlow: Coding = {
  code: '39636-6',
  display: 'Brain Scan flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainSPECTFlow: Coding = {
  code: '39637-4',
  display: 'Brain SPECT flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainSPECTWI123IV: Coding = {
  code: '39638-2',
  display: 'Brain SPECT W I-123 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainSPECTWTl201IV: Coding = {
  code: '39639-0',
  display: 'Brain SPECT W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainSPECTWTc99mDTPAIV: Coding = {
  code: '39640-8',
  display: 'Brain SPECT W Tc-99m DTPA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainSPECTWTc99mGlucoheptonateIV: Coding = {
  code: '39641-6',
  display: 'Brain SPECT W Tc-99m glucoheptonate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanWTc99mGlucoheptonateIV: Coding = {
  code: '39642-4',
  display: 'Brain Scan W Tc-99m glucoheptonate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainVeinsScan: Coding = {
  code: '39643-2',
  display: 'Brain veins Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastSPECT: Coding = {
  code: '39644-0',
  display: 'Breast SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastScanLimited: Coding = {
  code: '39645-7',
  display: 'Breast Scan limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastScan: Coding = {
  code: '39646-5',
  display: 'Breast Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTWTc99mTetrofosminIV: Coding = {
  code: '39647-3',
  display: 'Heart SPECT W Tc-99m Tetrofosmin IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTWDipyridamoleAndWRadionuclideIV: Coding = {
  code: '39648-1',
  display: 'Heart SPECT W dipyridamole and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECT: Coding = {
  code: '39649-9',
  display: 'Heart SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScan: Coding = {
  code: '39650-7',
  display: 'Heart Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanWAdenosineAndWTl201IV: Coding = {
  code: '39651-5',
  display: 'Heart Scan W adenosine and W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanWDobutamineAndWTl201IV: Coding = {
  code: '39652-3',
  display: 'Heart Scan W dobutamine and W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanForInfarct: Coding = {
  code: '39653-1',
  display: 'Heart Scan for infarct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTForInfarctWTc99mPYPIV: Coding = {
  code: '39654-9',
  display: 'Heart SPECT for infarct W Tc-99m PYP IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTForInfarctWTc99mSestamibiIV: Coding = {
  code: '39655-6',
  display: 'Heart SPECT for infarct W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTForInfarct: Coding = {
  code: '39656-4',
  display: 'Heart SPECT for infarct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanForInfarctWTc99mPYPIV: Coding = {
  code: '39657-2',
  display: 'Heart Scan for infarct W Tc-99m PYP IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTAtRestAndWRadionuclideIV: Coding = {
  code: '39658-0',
  display: 'Heart SPECT at rest and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanAtRestAndWDipyridamoleAndWRadionuclideIV: Coding = {
  code: '39660-6',
  display: 'Heart Scan at rest and W dipyridamole and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanAtRestAndWDobutamineAndWRadionuclideIV: Coding = {
  code: '39661-4',
  display: 'Heart Scan at rest and W dobutamine and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTAtRestAndWStressAndWTc99mSestamibiIV: Coding = {
  code: '39662-2',
  display: 'Heart SPECT at rest and W stress and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanAtRestAndWStressAndWRadionuclideIV: Coding = {
  code: '39663-0',
  display: 'Heart Scan at rest and W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanForShuntDetectionWTc99mMAAIV: Coding = {
  code: '39664-8',
  display: 'Heart Scan for shunt detection W Tc-99m MAA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanForShuntDetection: Coding = {
  code: '39665-5',
  display: 'Heart Scan for shunt detection',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanWStressAndW201ThIV: Coding = {
  code: '39666-3',
  display: 'Heart Scan W stress and W 201 Th IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanWStressAndWRadionuclideIV: Coding = {
  code: '39667-1',
  display: 'Heart Scan W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTWStressAndWRadionuclideIV: Coding = {
  code: '39668-9',
  display: 'Heart SPECT W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanWholeBodyWTc99mArcitumomabIV: Coding = {
  code: '39669-7',
  display: 'Scan whole body W Tc-99m Arcitumomab IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LacrimalDuctScanWRadionuclideIntraLacrimalDuct: Coding = {
  code: '39670-5',
  display: 'Lacrimal duct Scan W radionuclide intra lacrimal duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RectumScanWRadionuclidePO: Coding = {
  code: '39671-3',
  display: 'Rectum Scan W radionuclide PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EsophagusScanForMotilityWRadionuclidePO: Coding = {
  code: '39672-1',
  display: 'Esophagus Scan for motility W radionuclide PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EsophagusScanForRefluxWRadionuclidePO: Coding = {
  code: '39673-9',
  display: 'Esophagus Scan for reflux W radionuclide PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GallbladderScanWTc99mDISIDAIV: Coding = {
  code: '39674-7',
  display: 'Gallbladder Scan W Tc-99m DISIDA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTForInfectionWGA67IV: Coding = {
  code: '39675-4',
  display: 'SPECT for infection W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanStaticForInfectionWGA67IV: Coding = {
  code: '39676-2',
  display: 'Scan static for infection W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForInfectionWGA67IV: Coding = {
  code: '39677-0',
  display: 'Scan for infection W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTForTumorWGA67IV: Coding = {
  code: '39678-8',
  display: 'SPECT for tumor W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForTumorWGA67IV: Coding = {
  code: '39679-6',
  display: 'Scan for tumor W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTWholeBodyWGA67IV: Coding = {
  code: '39680-4',
  display: 'SPECT whole body W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTLimitedWGA67IV: Coding = {
  code: '39681-2',
  display: 'SPECT limited W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTWGA67IV: Coding = {
  code: '39682-0',
  display: 'SPECT W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanWholeBodyWGA67IV: Coding = {
  code: '39683-8',
  display: 'Scan whole body W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTForAbscessWGA67IV: Coding = {
  code: '39684-6',
  display: 'SPECT for abscess W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForAbscessWGA67IV: Coding = {
  code: '39685-3',
  display: 'Scan for abscess W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForLymphomaWGA67IV: Coding = {
  code: '39686-1',
  display: 'Scan for lymphoma W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanLimitedWGA67IV: Coding = {
  code: '39687-9',
  display: 'Scan limited W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanWGA67IV: Coding = {
  code: '39688-7',
  display: 'Scan W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineScanWTc99mSCIV: Coding = {
  code: '39689-5',
  display: 'Gastrointestine Scan W Tc-99m SC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverScanWTc99mTaggedRBCIV: Coding = {
  code: '39690-3',
  display: 'Liver Scan W Tc-99m tagged RBC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverSPECTWTc99mTaggedRBCIV: Coding = {
  code: '39691-1',
  display: 'Liver SPECT W Tc-99m tagged RBC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverSPECT: Coding = {
  code: '39692-9',
  display: 'Liver SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverScan: Coding = {
  code: '39693-7',
  display: 'Liver Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverTransplantScan: Coding = {
  code: '39694-5',
  display: 'Liver transplant Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanLimited: Coding = {
  code: '39695-2',
  display: 'Lung Scan limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanWDepreotideAndWRadionuclideIV: Coding = {
  code: '39696-0',
  display: 'Lung Scan W depreotide and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanPerfusion: Coding = {
  code: '39697-8',
  display: 'Lung Scan perfusion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanWholeBodyWI131MIBGIV: Coding = {
  code: '39698-6',
  display: 'Scan whole body W I-131 MIBG IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionAtRestAndWTc99mSestamibiIV: Coding = {
  code: '39699-4',
  display: 'Heart Scan perfusion at rest and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionWAdenosineAndWRadionuclideIV: Coding = {
  code: '39700-0',
  display: 'Heart SPECT perfusion W adenosine and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWAdenosineAndWRadionuclideIV: Coding = {
  code: '39701-8',
  display: 'Heart Scan perfusion W adenosine and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWDobutamineAndWTc99mSestamibiIV: Coding = {
  code: '39702-6',
  display: 'Heart Scan perfusion W dobutamine and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWDobutamineAndWRadionuclideIV: Coding = {
  code: '39703-4',
  display: 'Heart Scan perfusion W dobutamine and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWTc99mSestamibiIV: Coding = {
  code: '39704-2',
  display: 'Heart Scan perfusion W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWDipyridamoleAndWTc99mSestamibiIV: Coding = {
  code: '39705-9',
  display: 'Heart Scan perfusion W dipyridamole and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWDipyridamoleAndWTl201IV: Coding = {
  code: '39707-5',
  display: 'Heart Scan perfusion W dipyridamole and W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWDipyridamoleAndWRadionuclideIV: Coding = {
  code: '39708-3',
  display: 'Heart Scan perfusion W dipyridamole and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWDipyridamoleAndWTc99mIV: Coding = {
  code: '39709-1',
  display: 'Heart Scan perfusion W dipyridamole and W Tc-99m IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionWTc99mSestamibiIV: Coding = {
  code: '39710-9',
  display: 'Heart SPECT perfusion W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionWTl201IV: Coding = {
  code: '39711-7',
  display: 'Heart SPECT perfusion W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusion: Coding = {
  code: '39712-5',
  display: 'Heart SPECT perfusion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWTl201IVAndTc99mTetrofosminIV: Coding = {
  code: '39713-3',
  display: 'Heart Scan perfusion W Tl-201 IV and Tc-99m Tetrofosmin IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWTl201IV: Coding = {
  code: '39714-1',
  display: 'Heart Scan perfusion W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWStressAndWTl201IV: Coding = {
  code: '39715-8',
  display: 'Heart Scan perfusion W stress and W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusion: Coding = {
  code: '39716-6',
  display: 'Heart Scan perfusion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionAtRestAndWRadionuclideIV: Coding = {
  code: '39718-2',
  display: 'Heart SPECT perfusion at rest and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionAtRestAndWAdenosineAndWRadionuclideIV: Coding = {
  code: '39719-0',
  display: 'Heart Scan perfusion at rest and W adenosine and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionAtRestAndWDipyridamoleAndWTc99mSestamibiIV: Coding = {
  code: '39720-8',
  display: 'Heart Scan perfusion at rest and W dipyridamole and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionAtRestAndWDipyridamoleAndWRadionuclideIV: Coding = {
  code: '39722-4',
  display: 'Heart Scan perfusion at rest and W dipyridamole and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionAtRestAndWStressAndWTl201IV: Coding = {
  code: '39723-2',
  display: 'Heart SPECT perfusion at rest and W stress and W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionAtRestAndWStressAndWRadionuclideIV: Coding = {
  code: '39724-0',
  display: 'Heart SPECT perfusion at rest and W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionAtRestAndWAdenosineAndWTl201IV: Coding = {
  code: '39725-7',
  display: 'Heart SPECT perfusion at rest and W adenosine and W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionAtRestAndWStressAndWRadionuclideIV: Coding = {
  code: '39726-5',
  display: 'Heart Scan perfusion at rest and W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionAtRestAndWStressAndWTc99mSestamibiIV: Coding = {
  code: '39727-3',
  display: 'Heart Scan perfusion at rest and W stress and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionAtRestAndWRadionuclideIV: Coding = {
  code: '39728-1',
  display: 'Heart Scan perfusion at rest and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionAtRestAndWTl201IV: Coding = {
  code: '39729-9',
  display: 'Heart SPECT perfusion at rest and W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWStressAndWRadionuclideIV: Coding = {
  code: '39730-7',
  display: 'Heart Scan perfusion W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWAdenosineAndWTc99mSestamibiIV: Coding = {
  code: '39731-5',
  display: 'Heart Scan perfusion W adenosine and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWStressAndWTc99mSestamibiIV: Coding = {
  code: '39732-3',
  display: 'Heart Scan perfusion W stress and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWDobutamineAndWTl201IV: Coding = {
  code: '39733-1',
  display: 'Heart Scan perfusion W dobutamine and W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionWStressAndWRadionuclideIV: Coding = {
  code: '39734-9',
  display: 'Heart SPECT perfusion W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionWAdenosineAndWTl201IV: Coding = {
  code: '39735-6',
  display: 'Heart Scan perfusion W adenosine and W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionWStressAndWTc99mSestamibiIV: Coding = {
  code: '39736-4',
  display: 'Heart SPECT perfusion W stress and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckScan: Coding = {
  code: '39737-2',
  display: 'Neck Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenScanWIn111SatumomabIV: Coding = {
  code: '39738-0',
  display: 'Abdomen Scan W In-111 Satumomab IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PancreasScan: Coding = {
  code: '39739-8',
  display: 'Pancreas Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParathyroidSPECT: Coding = {
  code: '39740-6',
  display: 'Parathyroid SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParathyroidScanDelayed: Coding = {
  code: '39741-4',
  display: 'Parathyroid Scan delayed',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParathyroidScan: Coding = {
  code: '39742-2',
  display: 'Parathyroid Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProstateSPECTWTc99mCapromabPendatideIV: Coding = {
  code: '39743-0',
  display: 'Prostate SPECT W Tc-99m capromab pendatide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProstateScanWTc99mCapromabPendatideIV: Coding = {
  code: '39744-8',
  display: 'Prostate Scan W Tc-99m capromab pendatide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralScanWTc99mDTPAIV: Coding = {
  code: '39745-5',
  display: 'Kidney - bilateral Scan W Tc-99m DTPA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralScanWTc99mMertiatideIV: Coding = {
  code: '39746-3',
  display: 'Kidney - bilateral Scan W Tc-99m Mertiatide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SalivaryGlandScan: Coding = {
  code: '39747-1',
  display: 'Salivary gland Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTForTumorWTc99mSestamibiIV: Coding = {
  code: '39748-9',
  display: 'SPECT for tumor W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForTumorWholeBodyWTc99mSestamibiIV: Coding = {
  code: '39749-7',
  display: 'Scan for tumor whole body W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForTumorWTc99mSestamibiIV: Coding = {
  code: '39750-5',
  display: 'Scan for tumor W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpleenScan: Coding = {
  code: '39751-3',
  display: 'Spleen Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpleenScanWRadionuclideTaggedHeatDamagedRBCIV: Coding = {
  code: '39752-1',
  display: 'Spleen Scan W radionuclide tagged heat damaged RBC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleScanWTc99mDTPAIV: Coding = {
  code: '39753-9',
  display: 'Scrotum and Testicle Scan W Tc-99m DTPA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanLimitedWI131IV: Coding = {
  code: '39754-7',
  display: 'Thyroid Scan limited W I-131 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidSPECTWI131IV: Coding = {
  code: '39755-4',
  display: 'Thyroid SPECT W I-131 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanWTc99mSestamibiIV: Coding = {
  code: '39756-2',
  display: 'Thyroid Scan W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanWTc99mIV: Coding = {
  code: '39757-0',
  display: 'Thyroid Scan W Tc-99m IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanGuidanceForLocalizationOfTumorOfBreast: Coding = {
  code: '39758-8',
  display: 'Scan Guidance for localization of tumor of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTGuidanceForLocalizationOfTumorLimited: Coding = {
  code: '39759-6',
  display: 'SPECT Guidance for localization of tumor limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanGuidanceForLocalizationOfTumorLimited: Coding = {
  code: '39760-4',
  display: 'Scan Guidance for localization of tumor limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanGuidanceForLocalizationOfTumorLimitedWTc99mSestamibiIV: Coding = {
  code: '39761-2',
  display: 'Scan Guidance for localization of tumor limited-- W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTGuidanceForLocalizationOfTumor: Coding = {
  code: '39762-0',
  display: 'SPECT Guidance for localization of tumor',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanGuidanceForLocalizationOfTumor: Coding = {
  code: '39763-8',
  display: 'Scan Guidance for localization of tumor',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinScanWTc99mSCIV: Coding = {
  code: '39764-6',
  display: 'Vein Scan W Tc-99m SC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinScanWTc99mDTPAIV: Coding = {
  code: '39765-3',
  display: 'Vein Scan W Tc-99m DTPA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinScanWTc99mHDPIV: Coding = {
  code: '39766-1',
  display: 'Vein Scan W Tc-99m HDP IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_StomachScanForGastricEmptyingLiquidPhaseWRadionuclidePO: Coding = {
  code: '39767-9',
  display: 'Stomach Scan for gastric emptying liquid phase W radionuclide PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_StomachScanForGastricEmptyingWTc99mSCPO: Coding = {
  code: '39768-7',
  display: 'Stomach Scan for gastric emptying W Tc-99m SC PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_StomachScanForGastricEmptyingWRadionuclidePO: Coding = {
  code: '39769-5',
  display: 'Stomach Scan for gastric emptying W radionuclide PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineSPECT: Coding = {
  code: '39770-3',
  display: 'Gastrointestine SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTForAbscess: Coding = {
  code: '39811-5',
  display: 'SPECT for abscess',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanWTc99mHMPAOIV: Coding = {
  code: '39812-3',
  display: 'Bone Scan W Tc-99m HMPAO IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneSPECTLimited: Coding = {
  code: '39813-1',
  display: 'Bone SPECT limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanStaticLimited: Coding = {
  code: '39814-9',
  display: 'Bone Scan static limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanStatic: Coding = {
  code: '39815-6',
  display: 'Bone Scan static',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneSPECTWholeBody: Coding = {
  code: '39816-4',
  display: 'Bone SPECT whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanStaticWholeBody: Coding = {
  code: '39817-2',
  display: 'Bone Scan static whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanWholeBody: Coding = {
  code: '39818-0',
  display: 'Bone Scan whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanDelayed: Coding = {
  code: '39819-8',
  display: 'Bone Scan delayed',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanWSM153IV: Coding = {
  code: '39820-6',
  display: 'Bone Scan W SM153 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneMarrowSPECTLimited: Coding = {
  code: '39821-4',
  display: 'Bone marrow SPECT limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneMarrowScanLimited: Coding = {
  code: '39822-2',
  display: 'Bone marrow Scan limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneMarrowSPECT: Coding = {
  code: '39823-0',
  display: 'Bone marrow SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneMarrowScanStatic: Coding = {
  code: '39824-8',
  display: 'Bone marrow Scan static',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneMarrowSPECTWholeBody: Coding = {
  code: '39825-5',
  display: 'Bone marrow SPECT whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneMarrowScanWholeBody: Coding = {
  code: '39826-3',
  display: 'Bone marrow Scan whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForEndocrineTumorWholeBodyWI131MIBGIV: Coding = {
  code: '39827-1',
  display: 'Scan for endocrine tumor whole body W I-131 MIBG IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForEndocrineTumorWholeBodyWIn111PentetreotideIV: Coding = {
  code: '39828-9',
  display: 'Scan for endocrine tumor whole body W In-111 pentetreotide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForTumorWholeBodyWGA67IV: Coding = {
  code: '39829-7',
  display: 'Scan for tumor whole body W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForInfectionWholeBodyWGA67IV: Coding = {
  code: '39830-5',
  display: 'Scan for infection whole body W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForTumorLimitedWGA67IV: Coding = {
  code: '39831-3',
  display: 'Scan for tumor limited W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverScanStatic: Coding = {
  code: '39832-1',
  display: 'Liver Scan static',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanPerfusionWRadionuclideGaseousInhaled: Coding = {
  code: '39833-9',
  display: 'Lung Scan perfusion W radionuclide gaseous inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanVentilationWTc99mDTPAAerosolInhaled: Coding = {
  code: '39834-7',
  display: 'Lung Scan ventilation W Tc-99m DTPA aerosol inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanVentilationWRadionuclideAerosolInhaled: Coding = {
  code: '39835-4',
  display: 'Lung Scan ventilation W radionuclide aerosol inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanVentilationWRadionuclideGaseousInhaled: Coding = {
  code: '39836-2',
  display: 'Lung Scan ventilation W radionuclide gaseous inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanVentilationWRadionuclideInhaled: Coding = {
  code: '39837-0',
  display: 'Lung Scan ventilation W radionuclide inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungSPECTVentilationAndPerfusionWRadionuclideInhaledAndWRadionuclideIV: Coding = {
  code: '39838-8',
  display: 'Lung SPECT ventilation and perfusion W radionuclide inhaled and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTWI131MIBGIV: Coding = {
  code: '39839-6',
  display: 'SPECT W I-131 MIBG IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanDelayedWI131MIBGIV: Coding = {
  code: '39840-4',
  display: 'Scan delayed W I-131 MIBG IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanWI131MIBGIV: Coding = {
  code: '39841-2',
  display: 'Scan W I-131 MIBG IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanDelayedWIn111SatumomabIV: Coding = {
  code: '39842-0',
  display: 'Scan delayed W In-111 Satumomab IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanLimitedWIn111SatumomabIV: Coding = {
  code: '39843-8',
  display: 'Scan limited W In-111 Satumomab IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTWIn111SatumomabIV: Coding = {
  code: '39844-6',
  display: 'SPECT W In-111 Satumomab IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanWholeBodyWIn111SatumomabIV: Coding = {
  code: '39845-3',
  display: 'Scan whole body W In-111 Satumomab IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanWIn111SatumomabIV: Coding = {
  code: '39846-1',
  display: 'Scan W In-111 Satumomab IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandScanFlow: Coding = {
  code: '39847-9',
  display: 'Parotid gland Scan flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeritoneovenousShuntScanForPatencyWIn111IT: Coding = {
  code: '39848-7',
  display: 'Peritoneovenous shunt Scan for patency W In-111 IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeritoneovenousShuntScanForPatencyWRadionuclideIT: Coding = {
  code: '39849-5',
  display: 'Peritoneovenous shunt Scan for patency W radionuclide IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralScanWI131IV: Coding = {
  code: '39850-3',
  display: 'Kidney - bilateral Scan W I-131 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralSPECTWTc99mMertiatideIV: Coding = {
  code: '39851-1',
  display: 'Kidney - bilateral SPECT W Tc-99m Mertiatide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralSPECT: Coding = {
  code: '39852-9',
  display: 'Kidney - bilateral SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralScanStatic: Coding = {
  code: '39853-7',
  display: 'Kidney - bilateral Scan static',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralScanStaticWTc99mDMSAIV: Coding = {
  code: '39854-5',
  display: 'Kidney - bilateral Scan static W Tc-99m DMSA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleScanStatic: Coding = {
  code: '39855-2',
  display: 'Scrotum and Testicle Scan static',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanFlow: Coding = {
  code: '39856-0',
  display: 'Thyroid Scan flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalGlandScanWI131MIBGIV: Coding = {
  code: '39857-8',
  display: 'Adrenal gland Scan W I-131 MIBG IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanFlow: Coding = {
  code: '39858-6',
  display: 'Bone Scan flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanDelayedStatic: Coding = {
  code: '39859-4',
  display: 'Brain Scan delayed static',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanBloodPoolWStressAndWRadionuclideIV: Coding = {
  code: '39860-2',
  display: 'Heart Scan blood pool W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanBloodPool: Coding = {
  code: '39861-0',
  display: 'Heart Scan blood pool',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTBloodPoolAtRestAndWRadionuclideIV: Coding = {
  code: '39862-8',
  display: 'Heart SPECT blood pool at rest and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassAtRestAndWStressAndWRadionuclideIV: Coding = {
  code: '39863-6',
  display: 'Heart Scan first pass at rest and W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPass: Coding = {
  code: '39864-4',
  display: 'Heart Scan first pass',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LeftVentricleScanFirstPass: Coding = {
  code: '39865-1',
  display: 'Left ventricle Scan first pass',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassAtRestAndWTc99mSestamibiIV: Coding = {
  code: '39866-9',
  display: 'Heart Scan first pass at rest and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassAtRestAndWRadionuclideIV: Coding = {
  code: '39867-7',
  display: 'Heart Scan first pass at rest and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassWStressAndWTc99mSestamibiIV: Coding = {
  code: '39868-5',
  display: 'Heart Scan first pass W stress and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassWStressAndWRadionuclideIV: Coding = {
  code: '39869-3',
  display: 'Heart Scan first pass W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFlowWTc99mPertechnetateIV: Coding = {
  code: '39870-1',
  display: 'Heart Scan flow W Tc-99m pertechnetate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFlow: Coding = {
  code: '39871-9',
  display: 'Heart Scan flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTWallMotion: Coding = {
  code: '39872-7',
  display: 'Heart SPECT wall motion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanWallMotion: Coding = {
  code: '39873-5',
  display: 'Heart Scan wall motion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCisternScanDelayedWRadionuclideIT: Coding = {
  code: '39874-3',
  display: 'Head Cistern Scan delayed W radionuclide IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanDelayedWGA67IV: Coding = {
  code: '39875-0',
  display: 'Scan delayed W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverAndSpleenSPECT: Coding = {
  code: '39876-8',
  display: 'Liver and Spleen SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverAndSpleenScan: Coding = {
  code: '39877-6',
  display: 'Liver and Spleen Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverAndSpleenScanStatic: Coding = {
  code: '39878-4',
  display: 'Liver and Spleen Scan static',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneSPECT1Phase: Coding = {
  code: '39879-2',
  display: 'Bone SPECT 1 phase',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScan2ViewsPhase: Coding = {
  code: '39880-0',
  display: 'Bone Scan 2 views phase',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneSPECT3PhaseWholeBody: Coding = {
  code: '39881-8',
  display: 'Bone SPECT 3 phase whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScan3ViewsPhaseWholeBody: Coding = {
  code: '39882-6',
  display: 'Bone Scan 3 views phase whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScan3ViewsPhase: Coding = {
  code: '39883-4',
  display: 'Bone Scan 3 views phase',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanBloodPool: Coding = {
  code: '39884-2',
  display: 'Bone Scan blood pool',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassAndVentricularVolume: Coding = {
  code: '39885-9',
  display: 'Heart Scan first pass and ventricular volume',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassAndWallMotionAtRestAndWRadionuclideIV: Coding = {
  code: '39886-7',
  display: 'Heart Scan first pass and wall motion at rest and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassAndEjectionFractionAtRestAndWRadionuclideIV: Coding = {
  code: '39887-5',
  display: 'Heart Scan first pass and ejection fraction at rest and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassAndWallMotionWStressAndWRadionuclideIV: Coding = {
  code: '39888-3',
  display: 'Heart Scan first pass and wall motion W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassAndEjectionFraction: Coding = {
  code: '39889-1',
  display: 'Heart Scan first pass and ejection fraction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassAndWallMotion: Coding = {
  code: '39890-9',
  display: 'Heart Scan first pass and wall motion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanForInfarctAndFirstPassWTc99mPYPIV: Coding = {
  code: '39891-7',
  display: 'Heart Scan for infarct and first pass W Tc-99m PYP IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanForInfarctAndFirstPass: Coding = {
  code: '39892-5',
  display: 'Heart Scan for infarct and first pass',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFlowForShuntDetection: Coding = {
  code: '39893-3',
  display: 'Heart Scan flow for shunt detection',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanStaticForShuntDetection: Coding = {
  code: '39894-1',
  display: 'Heart Scan static for shunt detection',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GallbladderScanEjectionFractionWTc99mDISIDAIV: Coding = {
  code: '39895-8',
  display: 'Gallbladder Scan ejection fraction W Tc-99m DISIDA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanStaticForTumorWGA67IV: Coding = {
  code: '39896-6',
  display: 'Scan static for tumor W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverAndLungScan: Coding = {
  code: '39897-4',
  display: 'Liver and Lung Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungSPECTVentilationWRadionuclideAerosolInhaled: Coding = {
  code: '39898-2',
  display: 'Lung SPECT ventilation W radionuclide aerosol inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SalivaryGlandScanFlow: Coding = {
  code: '39899-0',
  display: 'Salivary gland Scan flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SalivaryGlandScanStatic: Coding = {
  code: '39900-6',
  display: 'Salivary gland Scan static',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScan3ViewsPhaseMultipleAreas: Coding = {
  code: '39901-4',
  display: 'Bone Scan 3 views phase multiple areas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScan3ViewsPhaseSingleArea: Coding = {
  code: '39902-2',
  display: 'Bone Scan 3 views phase single area',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanStaticMultipleAreas: Coding = {
  code: '39903-0',
  display: 'Bone Scan static multiple areas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanMultipleAreas: Coding = {
  code: '39904-8',
  display: 'Bone Scan multiple areas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneSPECTMultipleAreas: Coding = {
  code: '39905-5',
  display: 'Bone SPECT multiple areas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneMarrowSPECTMultipleAreas: Coding = {
  code: '39906-3',
  display: 'Bone marrow SPECT multiple areas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneMarrowScanMultipleAreas: Coding = {
  code: '39907-1',
  display: 'Bone marrow Scan multiple areas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassAndWallMotionAndVentricularVolumeWStressAndWRadionuclideIV: Coding = {
  code: '39908-9',
  display: 'Heart Scan first pass and wall motion and ventricular volume W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassAndWallMotionAndVentricularVolumeAndEjectionFractionWStressAndWRadionuclideIV: Coding = {
  code: '39909-7',
  display: 'Heart Scan first pass and wall motion and ventricular volume and ejection fraction W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassAndWallMotionAndEjectionFraction: Coding = {
  code: '39910-5',
  display: 'Heart Scan first pass and wall motion and ejection fraction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanFirstPassAndWallMotionAndVentricularVolumeAndEjectionFraction: Coding = {
  code: '39912-1',
  display: 'Heart Scan first pass and wall motion and ventricular volume and ejection fraction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTGatedAndEjectionFraction: Coding = {
  code: '39913-9',
  display: 'Heart SPECT gated and ejection fraction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedWTc99mSestamibiIV: Coding = {
  code: '39914-7',
  display: 'Heart Scan gated W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGated: Coding = {
  code: '39915-4',
  display: 'Heart Scan gated',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTGated: Coding = {
  code: '39916-2',
  display: 'Heart SPECT gated',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedAndEjectionFraction: Coding = {
  code: '39917-0',
  display: 'Heart Scan gated and ejection fraction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTGatedAndWallMotion: Coding = {
  code: '39918-8',
  display: 'Heart SPECT gated and wall motion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedAndFirstPass: Coding = {
  code: '39919-6',
  display: 'Heart Scan gated and first pass',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedAtRestAndWTc99mSestamibiIV: Coding = {
  code: '39920-4',
  display: 'Heart Scan gated at rest and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedAtRestAndWRadionuclideIV: Coding = {
  code: '39921-2',
  display: 'Heart Scan gated at rest and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedAtRestAndWTc99mPertechnetateIV: Coding = {
  code: '39922-0',
  display: 'Heart Scan gated at rest and W Tc-99m pertechnetate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedAndEjectionFractionAtRestAndWRadionuclideIV: Coding = {
  code: '39923-8',
  display: 'Heart Scan gated and ejection fraction at rest and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedAtRestAndWStressAndWRadionuclideIV: Coding = {
  code: '39924-6',
  display: 'Heart Scan gated at rest and W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedAndWallMotionAndEjectionFractionAtRestAndWRadionuclideIV: Coding = {
  code: '39925-3',
  display: 'Heart Scan gated and wall motion and ejection fraction at rest and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedWStressAndWTc99mPertechnetateIV: Coding = {
  code: '39927-9',
  display: 'Heart Scan gated W stress and W Tc-99m pertechnetate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedWStressAndWRadionuclideIV: Coding = {
  code: '39928-7',
  display: 'Heart Scan gated W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedAndWallMotionWStressAndWRadionuclideIV: Coding = {
  code: '39929-5',
  display: 'Heart Scan gated and wall motion W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTGatedWStressAndWRadionuclideIV: Coding = {
  code: '39930-3',
  display: 'Heart SPECT gated W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedAndWallMotionAndEjectionFraction: Coding = {
  code: '39931-1',
  display: 'Heart Scan gated and wall motion and ejection fraction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanWallMotionAndEjectionFraction: Coding = {
  code: '39932-9',
  display: 'Heart Scan wall motion and ejection fraction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForInfectionMultipleAreasWGA67IV: Coding = {
  code: '39933-7',
  display: 'Scan for infection multiple areas W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForTumorMultipleAreasWGA67IV: Coding = {
  code: '39934-5',
  display: 'Scan for tumor multiple areas W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanMultipleAreasWGA67IV: Coding = {
  code: '39935-2',
  display: 'Scan multiple areas W GA-67 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JointScanLimited: Coding = {
  code: '39936-0',
  display: 'Joint Scan limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JointScanMultipleAreas: Coding = {
  code: '39937-8',
  display: 'Joint Scan multiple areas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JointSPECT: Coding = {
  code: '39938-6',
  display: 'Joint SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JointScan: Coding = {
  code: '39939-4',
  display: 'Joint Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanClearanceWTc99mDTPAAerosolInhaled: Coding = {
  code: '39940-2',
  display: 'Lung Scan Clearance W Tc-99m DTPA aerosol inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanPerfusionWParticulateRadionuclideIV: Coding = {
  code: '39941-0',
  display: 'Lung Scan perfusion W particulate radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanVentilationAndPerfusionWRadionuclideInhaledSingleBreathAndWParticulateRadionuclideIV: Coding = {
  code: '39942-8',
  display: 'Lung Scan ventilation and perfusion W radionuclide inhaled single breath and W particulate radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanVentilationAndPerfusionWRadionuclideInhaledAndWParticulateRadionuclideIV: Coding = {
  code: '39943-6',
  display: 'Lung Scan ventilation and perfusion W radionuclide inhaled and W particulate radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanVentilationAndEquilibriumAndWashoutWRadionuclideInhaled: Coding = {
  code: '39944-4',
  display: 'Lung Scan ventilation and equilibrium and washout W radionuclide inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanVentilationWRadionuclideGaseousInhaledSingleBreath: Coding = {
  code: '39945-1',
  display: 'Lung Scan ventilation W radionuclide gaseous inhaled single breath',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanVentilationAndPerfusionAndDifferentialWRadionuclideInhaledAndWRadionuclideIV: Coding = {
  code: '39946-9',
  display: 'Lung Scan ventilation and perfusion and differential W radionuclide inhaled and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanVentilationAndEquilibriumWRadionuclideInhaledSingleBreath: Coding = {
  code: '39947-7',
  display: 'Lung Scan ventilation and equilibrium W radionuclide inhaled single breath',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanVentilationAndEquilibriumAndWashoutWRadionuclideInhaledSingleBreath: Coding = {
  code: '39948-5',
  display: 'Lung Scan ventilation and equilibrium and washout W radionuclide inhaled single breath',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanMultipleAreasWIn111SatumomabIV: Coding = {
  code: '39949-3',
  display: 'Scan multiple areas W In-111 Satumomab IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProstateScanMultipleAreasWTc99mCapromabPendatideIV: Coding = {
  code: '39950-1',
  display: 'Prostate Scan multiple areas W Tc-99m capromab pendatide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForTumorMultipleAreaWTc99mSestamibiIV: Coding = {
  code: '39951-9',
  display: 'Scan for tumor multiple area W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleScanStaticAndFlow: Coding = {
  code: '39952-7',
  display: 'Scrotum and Testicle Scan static and flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanGuidanceForLocalizationOfTumorMultipleAreas: Coding = {
  code: '39953-5',
  display: 'Scan Guidance for localization of tumor multiple areas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinScanForThrombosis: Coding = {
  code: '39954-3',
  display: 'Vein Scan for thrombosis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteronePresenceInSerumOrPlasma: Coding = {
  code: '40811-2',
  display: '11-Deoxycorticosterone [Presence] in Serum or Plasma',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma1HourPostXXXChallenge: Coding = {
  code: '40816-1',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --1 hour post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMolesVolumeIn24HourUrine: Coding = {
  code: '40818-7',
  display: '11-Deoxycorticosterone [Moles/volume] in 24 hour Urine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GallbladderScanWCholecystokininAndWRadionuclideIV: Coding = {
  code: '41770-9',
  display: 'Gallbladder Scan W cholecystokinin and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralScanWTc99mDMSAIV: Coding = {
  code: '41771-7',
  display: 'Kidney - bilateral Scan W Tc-99m DMSA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneSPECTWIn111TaggedWBCIV: Coding = {
  code: '41772-5',
  display: 'Bone SPECT W In-111 tagged WBC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesXRayPortable: Coding = {
  code: '41773-3',
  display: 'Facial bones X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckXRayLateralPortable: Coding = {
  code: '41774-1',
  display: 'Neck X-ray lateral portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRaySingleViewPortable: Coding = {
  code: '41775-8',
  display: 'Pelvis X-ray Single view portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipRightXRayAPAndLateralFrogPortable: Coding = {
  code: '41776-6',
  display: 'Pelvis and Hip - right X-ray AP and lateral frog portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightXRayAPAndLateralPortable: Coding = {
  code: '41777-4',
  display: 'Hip - right X-ray AP and lateral portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurRightXRayPortable: Coding = {
  code: '41778-2',
  display: 'Femur - right X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayPortable: Coding = {
  code: '41779-0',
  display: 'Knee - right X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayPortable: Coding = {
  code: '41782-4',
  display: 'Ankle - right X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayPortable: Coding = {
  code: '41783-2',
  display: 'Shoulder - right X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusRightXRayPortable: Coding = {
  code: '41784-0',
  display: 'Humerus - right X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRayLimited: Coding = {
  code: '41785-7',
  display: 'Elbow - right X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRayPortable: Coding = {
  code: '41786-5',
  display: 'Elbow - right X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayPortable: Coding = {
  code: '41787-3',
  display: 'Wrist - right X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRayPortable: Coding = {
  code: '41788-1',
  display: 'Hand - right X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRayLimited: Coding = {
  code: '41789-9',
  display: 'Hand - right X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayDuringSurgery: Coding = {
  code: '41790-7',
  display: 'Chest X-ray during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsRightXRayPortable: Coding = {
  code: '41791-5',
  display: 'Ribs - right X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayRightObliqueAndLeftOblique: Coding = {
  code: '41792-3',
  display: 'Chest X-ray right oblique and left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayDuringSurgery: Coding = {
  code: '41793-1',
  display: 'Abdomen X-ray during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperGastrointestineAndSmallBowelFluoroscopyWAirContrastPO: Coding = {
  code: '41795-6',
  display: 'Upper Gastrointestine and Small bowel Fluoroscopy W air contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColonFluoroscopyLimitedWAirAndBariumContrastPR: Coding = {
  code: '41797-2',
  display: 'Colon Fluoroscopy limited W air and barium contrast PR',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfProstate: Coding = {
  code: '41798-0',
  display: 'US Guidance for drainage of Prostate',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPlacementOfTubeInLiver: Coding = {
  code: '41799-8',
  display: 'Fluoroscopy Guidance for placement of tube in Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForDrainageOfPharynx: Coding = {
  code: '41800-4',
  display: 'Fluoroscopy Guidance for drainage of Pharynx',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInPortalVeinWContrastIV: Coding = {
  code: '41801-2',
  display: 'Fluoroscopic angiogram Guidance for placement of catheter in Portal vein-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfProstate: Coding = {
  code: '41802-0',
  display: 'Fluoroscopy Guidance for biopsy of Prostate',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfBreast: Coding = {
  code: '41803-8',
  display: 'Fluoroscopy Guidance for needle biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCTAnd3DReconstruction: Coding = {
  code: '41804-6',
  display: 'Unspecified body region CT and 3D reconstruction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenCT: Coding = {
  code: '41806-1',
  display: 'Abdomen CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitCT: Coding = {
  code: '41807-9',
  display: 'Orbit CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesAndMaxillaCT: Coding = {
  code: '41808-7',
  display: 'Facial bones and Maxilla CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfAbdomenRetroperitoneum: Coding = {
  code: '41809-5',
  display: 'US Guidance for drainage of Abdomen retroperitoneum',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForRemovalOfFluidFromAbdomen: Coding = {
  code: '41810-3',
  display: 'CT Guidance for removal of fluid from Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsBilateralAndChestXRayAndPAChest: Coding = {
  code: '41811-1',
  display: 'Ribs - bilateral and Chest X-ray and PA chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityArteryUSLimited: Coding = {
  code: '41812-9',
  display: 'Lower extremity artery US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteryUSLimited: Coding = {
  code: '41813-7',
  display: 'Upper extremity artery US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteryRightUS: Coding = {
  code: '41814-5',
  display: 'Upper extremity artery - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityArteryRightUS: Coding = {
  code: '41815-2',
  display: 'Lower extremity artery - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinsRightUS: Coding = {
  code: '41816-0',
  display: 'Extremity veins - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayAPAndLateralPortable: Coding = {
  code: '41817-8',
  display: 'Hip - left X-ray AP and lateral portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurLeftXRayPortable: Coding = {
  code: '41818-6',
  display: 'Femur - left X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay2ViewsAndTunnel: Coding = {
  code: '41819-4',
  display: 'Knee - left X-ray 2 views and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayPortable: Coding = {
  code: '41820-2',
  display: 'Knee - left X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayPortable: Coding = {
  code: '41823-6',
  display: 'Ankle - left X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayPortable: Coding = {
  code: '41824-4',
  display: 'Shoulder - left X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusLeftXRayPortable: Coding = {
  code: '41825-1',
  display: 'Humerus - left X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRayLimited: Coding = {
  code: '41826-9',
  display: 'Elbow - left X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRayPortable: Coding = {
  code: '41827-7',
  display: 'Elbow - left X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayPortable: Coding = {
  code: '41828-5',
  display: 'Wrist - left X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRayPortable: Coding = {
  code: '41829-3',
  display: 'Hand - left X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRayLimited: Coding = {
  code: '41830-1',
  display: 'Hand - left X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLeftXRayPortable: Coding = {
  code: '41831-9',
  display: 'Ribs - left X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLeftAndChestXRayAndPAChest: Coding = {
  code: '41832-7',
  display: 'Ribs - left and Chest X-ray and PA chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteryLeftUS: Coding = {
  code: '41833-5',
  display: 'Upper extremity artery - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityArteryLeftUS: Coding = {
  code: '41834-3',
  display: 'Lower extremity artery - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinsLeftUS: Coding = {
  code: '41835-0',
  display: 'Extremity veins - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanLimitedWIn111TaggedWBCIV: Coding = {
  code: '41836-8',
  display: 'Bone Scan limited W In-111 tagged WBC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTWholeBodyWTc99mArcitumomabIV: Coding = {
  code: '41837-6',
  display: 'SPECT whole body W Tc-99m Arcitumomab IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProstateSPECTWIn111SatumomabIV: Coding = {
  code: '41838-4',
  display: 'Prostate SPECT W In-111 Satumomab IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidBilateralXRayLimited: Coding = {
  code: '42007-5',
  display: 'Mastoid - bilateral X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusXRayDuringSurgery: Coding = {
  code: '42008-3',
  display: 'Humerus X-ray during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRay2ViewsAndApical: Coding = {
  code: '42009-1',
  display: 'Chest X-ray 2 views and apical',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsRightAndChestXRayAndPAChest: Coding = {
  code: '42010-9',
  display: 'Ribs - right and Chest X-ray and PA chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenXRayAPAndPAChest: Coding = {
  code: '42011-7',
  display: 'Chest and Abdomen X-ray AP and PA chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineUpperFluoroscopyWWaterSolubleContrastPO: Coding = {
  code: '42012-5',
  display: 'Gastrointestine upper Fluoroscopy W water soluble contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrinaryBladderAndUrethraFluoroscopyWContrast: Coding = {
  code: '42014-1',
  display: 'Urinary Bladder and Urethra Fluoroscopy W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForReplacementOfPercutaneousCholecystostomyInAbdomen: Coding = {
  code: '42017-4',
  display: 'Fluoroscopy Guidance for replacement of percutaneous cholecystostomy in Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VeinFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIA: Coding = {
  code: '42018-2',
  display: 'Vein Fluoroscopic angiogram Percutaneous transluminal angioplasty of vessel W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPUprightAndLeftLateralDecubitus: Coding = {
  code: '42019-0',
  display: 'Abdomen X-ray AP (upright and left lateral decubitus)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleLocalizationOfSpineLumbar: Coding = {
  code: '42020-8',
  display: 'CT Guidance for needle localization of Spine Lumbar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleLocalizationOfSpineCervical: Coding = {
  code: '42021-6',
  display: 'CT Guidance for needle localization of Spine Cervical',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastUSScreening: Coding = {
  code: '42132-1',
  display: 'Breast US screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAbscessDrainageOfLiver: Coding = {
  code: '42133-9',
  display: 'US Guidance for abscess drainage of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfThyroid: Coding = {
  code: '42134-7',
  display: 'US Guidance for aspiration of Thyroid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfSuperficialBone: Coding = {
  code: '42135-4',
  display: 'US Guidance for biopsy of Superficial bone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfHeart: Coding = {
  code: '42136-2',
  display: 'CT Guidance for biopsy of Heart',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfMediastinum: Coding = {
  code: '42137-0',
  display: 'US Guidance for biopsy of Mediastinum',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForPercutaneousBiopsyOfMuscle: Coding = {
  code: '42139-6',
  display: 'US Guidance for percutaneous biopsy of Muscle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForPlacementOfTubeInChest: Coding = {
  code: '42140-4',
  display: 'US Guidance for placement of tube in Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForRemovalOfCatheterFromCentralVeinTunneled: Coding = {
  code: '42141-2',
  display: 'US Guidance for removal of catheter from Central vein-- Tunneled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UterusAndFallopianTubesUSWSalineIntrauterine: Coding = {
  code: '42143-8',
  display: 'Uterus and Fallopian tubes US W saline intrauterine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinRightUS: Coding = {
  code: '42144-6',
  display: 'Extremity vein - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinLeftUS: Coding = {
  code: '42145-3',
  display: 'Extremity vein - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryUSDoppler: Coding = {
  code: '42146-1',
  display: 'Carotid artery US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacGraftUSDoppler: Coding = {
  code: '42147-9',
  display: 'Iliac graft US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartUS: Coding = {
  code: '42148-7',
  display: 'Heart US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryLeftUSLimited: Coding = {
  code: '42149-5',
  display: 'Carotid artery - left US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacGraftUSDopplerLimited: Coding = {
  code: '42150-3',
  display: 'Iliac graft US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryRightUSLimited: Coding = {
  code: '42151-1',
  display: 'Carotid artery - right US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisVesselsUSDopplerLimited: Coding = {
  code: '42152-9',
  display: 'Pelvis vessels US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityXRaySingleView: Coding = {
  code: '42153-7',
  display: 'Extremity X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VesselsFluoroscopicAngiogramWContrastIA: Coding = {
  code: '42156-0',
  display: 'Vessels Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVesselsFluoroscopicAngiogramWContrastIV: Coding = {
  code: '42157-8',
  display: 'Extremity vessels Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalGlandScan: Coding = {
  code: '42158-6',
  display: 'Adrenal gland Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SellaTurcicaXRay: Coding = {
  code: '42159-4',
  display: 'Sella turcica X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShuntXRay: Coding = {
  code: '42160-2',
  display: 'Shunt X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanWDobutamineAndWRadionuclideIV: Coding = {
  code: '42161-0',
  display: 'Heart Scan W dobutamine and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineUpperFluoroscopyAndAPWWaterSolubleContrastPO: Coding = {
  code: '42162-8',
  display: 'Gastrointestine upper Fluoroscopy and AP W water soluble contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAndOblique: Coding = {
  code: '42163-6',
  display: 'Spine Lumbar X-ray and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAndOblique: Coding = {
  code: '42164-4',
  display: 'Spine Cervical X-ray and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsAndChestXRayAndPAChest: Coding = {
  code: '42165-1',
  display: 'Ribs and Chest X-ray and PA chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScan2ViewsAtRestAndWTl201IV: Coding = {
  code: '42166-9',
  display: 'Heart Scan 2 views at rest and W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipBilateralXRayAPAndLateralFrog: Coding = {
  code: '42167-7',
  display: 'Pelvis and Hip - bilateral X-ray AP and lateral frog',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightFFDMammogramDiagnostic: Coding = {
  code: '42168-5',
  display: 'Breast - right FFD mammogram diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftFFDMammogramDiagnostic: Coding = {
  code: '42169-3',
  display: 'Breast - left FFD mammogram diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForLymphoma: Coding = {
  code: '42170-1',
  display: 'Scan for lymphoma',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForTumorWholeBody: Coding = {
  code: '42171-9',
  display: 'Scan for tumor whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralFFDMammogramScreening: Coding = {
  code: '42174-3',
  display: 'Breast - bilateral FFD mammogram screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanWholeBody: Coding = {
  code: '42175-0',
  display: 'Scan whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertDrugAbuseAndDependenceSection: Coding = {
  code: '42227-9',
  display: 'FDA package insert Drug abuse and dependence section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPregnancySection: Coding = {
  code: '42228-7',
  display: 'FDA package insert Pregnancy section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertStructuredPatientLabellingUnclassifiedSection: Coding = {
  code: '42229-5',
  display: 'FDA package insert Structured patient labelling unclassified section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertStructuredProductLaballingPatientPackageInsertSection: Coding = {
  code: '42230-3',
  display: 'FDA package insert Structured product laballing patient package insert section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertStructuredProductLabellingMedguideSection: Coding = {
  code: '42231-1',
  display: 'FDA package insert Structured product labelling medguide section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPrecautionsSection: Coding = {
  code: '42232-9',
  display: 'FDA package insert Precautions section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfUnspecifiedBodyRegionWContrastIV: Coding = {
  code: '42260-0',
  display: 'CT Guidance for biopsy of Unspecified body region-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralScanFlow: Coding = {
  code: '42261-8',
  display: 'Kidney - bilateral Scan flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverScanFlow: Coding = {
  code: '42262-6',
  display: 'Liver Scan flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpleenScanFlow: Coding = {
  code: '42263-4',
  display: 'Spleen Scan flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfSuperficialBone: Coding = {
  code: '42265-9',
  display: 'CT Guidance for biopsy of Superficial bone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfSuperficialBone: Coding = {
  code: '42266-7',
  display: 'CT Guidance for needle biopsy of Superficial bone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfLymphNode: Coding = {
  code: '42267-5',
  display: 'CT Guidance for needle biopsy of Lymph node',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityCTWAndWOContrastIV: Coding = {
  code: '42268-3',
  display: 'Extremity CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenXRay: Coding = {
  code: '42269-1',
  display: 'Chest and Abdomen X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalMRIWFlexionAndWExtension: Coding = {
  code: '42270-9',
  display: 'Spine Cervical MRI W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanAndUptakeWI123IV: Coding = {
  code: '42271-7',
  display: 'Thyroid Scan and uptake W I-123 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayPAAndLateral: Coding = {
  code: '42272-5',
  display: 'Chest X-ray PA and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRay6Views: Coding = {
  code: '42273-3',
  display: 'Ankle - bilateral X-ray 6 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenAndPelvisCTWAndWOContrastIV: Coding = {
  code: '42274-1',
  display: 'Abdomen and Pelvis CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenCTWContrastIV: Coding = {
  code: '42275-8',
  display: 'Chest and Abdomen CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenCTWOContrast: Coding = {
  code: '42276-6',
  display: 'Chest and Abdomen CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenCTWAndWOContrastIV: Coding = {
  code: '42277-4',
  display: 'Chest and Abdomen CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityCTWOContrast: Coding = {
  code: '42278-2',
  display: 'Extremity CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfKidney: Coding = {
  code: '42279-0',
  display: 'CT Guidance for biopsy of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAbscessDrainageOfAppendix: Coding = {
  code: '42280-8',
  display: 'CT Guidance for abscess drainage of Appendix',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAbscessDrainageOfChest: Coding = {
  code: '42281-6',
  display: 'CT Guidance for abscess drainage of Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAbscessDrainageOfLiver: Coding = {
  code: '42282-4',
  display: 'CT Guidance for abscess drainage of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfPancreas: Coding = {
  code: '42283-2',
  display: 'CT Guidance for drainage of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAbscessDrainageOfPleuralSpace: Coding = {
  code: '42284-0',
  display: 'CT Guidance for abscess drainage of Pleural space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAbscessDrainageOfKidney: Coding = {
  code: '42285-7',
  display: 'CT Guidance for abscess drainage of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAbscessDrainageOfPelvis: Coding = {
  code: '42286-5',
  display: 'CT Guidance for abscess drainage of Pelvis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfAbdomenRetroperitoneum: Coding = {
  code: '42287-3',
  display: 'CT Guidance for drainage of Abdomen retroperitoneum',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfAbdomen: Coding = {
  code: '42288-1',
  display: 'CT Guidance for needle biopsy of Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfKidney: Coding = {
  code: '42289-9',
  display: 'CT Guidance for needle biopsy of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfPancreas: Coding = {
  code: '42290-7',
  display: 'CT Guidance for needle biopsy of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenRetroperitoneumCTWOContrast: Coding = {
  code: '42291-5',
  display: 'Abdomen retroperitoneum CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTForTumorWTl201IV: Coding = {
  code: '42292-3',
  display: 'SPECT for tumor W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVesselsCTAngiogramWOContrast: Coding = {
  code: '42293-1',
  display: 'Head vessels CT angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisVesselsCTAngiogramWContrastIV: Coding = {
  code: '42294-9',
  display: 'Pelvis vessels CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsCTAngiogramWContrastIV: Coding = {
  code: '42295-6',
  display: 'Upper extremity vessels CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForLocalizationOfBreastLeft: Coding = {
  code: '42296-4',
  display: 'Mammogram Guidance for localization of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForLocalizationOfBreastRight: Coding = {
  code: '42297-2',
  display: 'Mammogram Guidance for localization of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionMRIWAndWOContrastIV: Coding = {
  code: '42298-0',
  display: 'Unspecified body region MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleMRIWAndWOContrastIV: Coding = {
  code: '42299-8',
  display: 'Clavicle MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidMRI: Coding = {
  code: '42300-4',
  display: 'Thyroid MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UterusMRI: Coding = {
  code: '42301-2',
  display: 'Uterus MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleMRIWOContrast: Coding = {
  code: '42302-0',
  display: 'Clavicle MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitAndFaceMRI: Coding = {
  code: '42303-8',
  display: 'Orbit and Face MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadVesselsAndNeckVesselsMRIAngiogram: Coding = {
  code: '42304-6',
  display: 'Head vessels and Neck vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForTumorWTl201IV: Coding = {
  code: '42305-3',
  display: 'Scan for tumor W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanGatedAndWallMotion: Coding = {
  code: '42306-1',
  display: 'Heart Scan gated and wall motion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleScanFlow: Coding = {
  code: '42308-7',
  display: 'Scrotum and Testicle Scan flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanAtRestAndWStressAndWTl201IV: Coding = {
  code: '42309-5',
  display: 'Heart Scan at rest and W stress and W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneySPECT: Coding = {
  code: '42310-3',
  display: 'Kidney SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitLeftXRayForForeignBody: Coding = {
  code: '42311-1',
  display: 'Orbit - left X-ray for foreign body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitRightXRayForForeignBody: Coding = {
  code: '42312-9',
  display: 'Orbit - right X-ray for foreign body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLeftXRaySingleView: Coding = {
  code: '42313-7',
  display: 'Ribs - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsRightXRaySingleView: Coding = {
  code: '42314-5',
  display: 'Ribs - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfChestPleura: Coding = {
  code: '42333-5',
  display: 'US Guidance for biopsy of Chest.pleura',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfMammaryArteryInternalLeft: Coding = {
  code: '42334-3',
  display: 'Fluoroscopy Guidance for injection of Mammary artery.internal - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalFluoroscopyLimitedWContrastIT: Coding = {
  code: '42335-0',
  display: 'Spine Cervical Fluoroscopy limited W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainCTWXe133Inhaled: Coding = {
  code: '42377-2',
  display: 'Brain CT W Xe-133 inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPSingleViewWLeftBending: Coding = {
  code: '42378-0',
  display: 'Spine Lumbar X-ray AP single view W left bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPSingleViewWRightBending: Coding = {
  code: '42379-8',
  display: 'Spine Lumbar X-ray AP single view W right bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayAPAndLateralStanding: Coding = {
  code: '42380-6',
  display: 'Ankle - left X-ray AP and lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLowerPosteriorLeftXRay: Coding = {
  code: '42381-4',
  display: 'Ribs lower posterior - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayLateralAndMortiseAndBrodenWManualStress: Coding = {
  code: '42382-2',
  display: 'Ankle - left X-ray lateral and Mortise and Broden W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GallbladderXRayWDoubleDoseContrastPO: Coding = {
  code: '42383-0',
  display: 'Gallbladder X-ray W double dose contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainAndPituitaryAndSellaTurcicaMRI: Coding = {
  code: '42385-5',
  display: 'Brain and Pituitary and Sella turcica MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainMRICineForCSFFlow: Coding = {
  code: '42386-3',
  display: 'Brain MRI cine for CSF flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionMRICineForCSFFlow: Coding = {
  code: '42387-1',
  display: 'Unspecified body region MRI cine for CSF flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProstateMRIWEndorectalCoil: Coding = {
  code: '42388-9',
  display: 'Prostate MRI W endorectal coil',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisMRIWEndorectalCoil: Coding = {
  code: '42389-7',
  display: 'Pelvis MRI W endorectal coil',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TransvaginalMRI: Coding = {
  code: '42390-5',
  display: 'Transvaginal MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainAndPituitaryAndSellaTurcicaMRIWContrastIV: Coding = {
  code: '42391-3',
  display: 'Brain and Pituitary and Sella turcica MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainAndPituitaryAndSellaTurcicaMRIWAndWOContrastIV: Coding = {
  code: '42392-1',
  display: 'Brain and Pituitary and Sella turcica MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainAndPituitaryAndSellaTurcicaMRIWOContrast: Coding = {
  code: '42393-9',
  display: 'Brain and Pituitary and Sella turcica MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonarySystemCTWXe133Inhaled: Coding = {
  code: '42394-7',
  display: 'Pulmonary system CT W Xe-133 inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootSesamoidBonesBilateralXRayAxial: Coding = {
  code: '42395-4',
  display: 'Foot sesamoid bones - bilateral X-ray axial',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootSesamoidBonesLeftXRayAxial: Coding = {
  code: '42396-2',
  display: 'Foot sesamoid bones - left X-ray axial',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayFrontalStereo: Coding = {
  code: '42397-0',
  display: 'Chest X-ray frontal stereo',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayObliqueAndAPAndLateralStanding: Coding = {
  code: '42398-8',
  display: 'Foot X-ray oblique and (AP and lateral) standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootSesamoidBonesXRay: Coding = {
  code: '42399-6',
  display: 'Foot sesamoid bones X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootSesamoidBonesBilateralXRay: Coding = {
  code: '42400-2',
  display: 'Foot sesamoid bones - bilateral X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPWRBendingAndWLBendingAndWOBendingAndLateral: Coding = {
  code: '42401-0',
  display: 'Spine Lumbar X-ray (AP W R-bending and W L-bending and WO bending) and Lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionXRayPostMortem: Coding = {
  code: '42402-8',
  display: 'Unspecified body region X-ray post mortem',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPWRightBendingAndWLeftBending: Coding = {
  code: '42403-6',
  display: 'Spine Lumbar X-ray AP W right bending and W left bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayAPAndLateralAndMeasurement: Coding = {
  code: '42404-4',
  display: 'Hip - left X-ray AP and lateral and measurement',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayAPPowerStandingAndLateralPowerWHyperextension: Coding = {
  code: '42405-1',
  display: 'Knee X-ray (AP^standing) and (lateral^W hyperextension)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPWAndWOLeftBending: Coding = {
  code: '42406-9',
  display: 'Spine Lumbar X-ray AP W and WO left bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPWAndWORightBending: Coding = {
  code: '42407-7',
  display: 'Spine Lumbar X-ray AP W and WO right bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPWRightBendingAndWLeftBendingAndWOBending: Coding = {
  code: '42408-5',
  display: 'Spine Lumbar X-ray AP W right bending and W left bending and WO bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootSesamoidBonesXRayAPAndLateral: Coding = {
  code: '42409-3',
  display: 'Foot sesamoid bones X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPAndLateralAndObliqueAndSpotStanding: Coding = {
  code: '42410-1',
  display: 'Spine Lumbar X-ray AP and lateral and oblique and spot standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayAPPowerWRBendingAndWLBendingAndLateralPowerWFlexionAndWExtension: Coding = {
  code: '42411-9',
  display: 'Spine Lumbar X-ray (AP^W R-bending and W L-bending) and (lateral^W flexion and W extension)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRay90DegreeAbduction: Coding = {
  code: '42412-7',
  display: 'Shoulder - left X-ray 90 degree abduction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayWRightBendingAndWLeftBending: Coding = {
  code: '42413-5',
  display: 'Spine Lumbar X-ray W right bending and W left bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayRightObliqueAndLeftObliqueWNippleMarkers: Coding = {
  code: '42414-3',
  display: 'Chest X-ray right oblique and left oblique W nipple markers',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramPostWirePlacement: Coding = {
  code: '42415-0',
  display: 'Breast - bilateral Mammogram Post Wire Placement',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramPostWirePlacement: Coding = {
  code: '42416-8',
  display: 'Breast - left Mammogram Post Wire Placement',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRayAPAndLateralAndObliqueWManualStress: Coding = {
  code: '42417-6',
  display: 'Ankle - bilateral X-ray AP and lateral and oblique W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayAPAndLateralAndObliqueWManualStress: Coding = {
  code: '42418-4',
  display: 'Ankle - left X-ray AP and lateral and oblique W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRaySingleView: Coding = {
  code: '42419-2',
  display: 'Wrist - bilateral X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayAPSingleViewStanding: Coding = {
  code: '42420-0',
  display: 'Pelvis X-ray AP single view standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfUnspecifiedBodyRegion: Coding = {
  code: '42421-8',
  display: 'Fluoroscopy Guidance for percutaneous drainage of abscess of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfBreast: Coding = {
  code: '42422-6',
  display: 'Fluoroscopy Guidance for percutaneous drainage of abscess of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfChest: Coding = {
  code: '42423-4',
  display: 'Fluoroscopy Guidance for percutaneous drainage of abscess of Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPAndLateralSitting: Coding = {
  code: '42424-2',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis AP and lateral sitting',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPStandingAndWRightBendingAndWLeftBendingAndWOBending: Coding = {
  code: '42425-9',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis AP standing and W right bending and W left bending and WO bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPSitting: Coding = {
  code: '42426-7',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis AP sitting',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisLateralSitting: Coding = {
  code: '42427-5',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis lateral sitting',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPStandingAndInBrace: Coding = {
  code: '42428-3',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis AP standing and in brace',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPStandingAndWRightBending: Coding = {
  code: '42429-1',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis AP standing and W right bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay2ViewsAndViewsStanding: Coding = {
  code: '42430-9',
  display: 'Knee - right X-ray 2 views and (views standing)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay30DegreeStanding: Coding = {
  code: '42431-7',
  display: 'Knee - right X-ray 30 degree standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRaySunriseAndViewsStanding: Coding = {
  code: '42432-5',
  display: 'Knee - right X-ray Sunrise and (views standing)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceStereotacticForCoreNeedleBiopsyOfBreastRight: Coding = {
  code: '42433-3',
  display: 'Mammogram Guidance.stereotactic for core needle biopsy of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootSesamoidBonesRightXRay: Coding = {
  code: '42434-1',
  display: 'Foot sesamoid bones - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SellaTurcicaXRay2Views: Coding = {
  code: '42435-8',
  display: 'Sella turcica X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SellaTurcicaXRayLateralAndTowne: Coding = {
  code: '42436-6',
  display: 'Sella turcica X-ray lateral and Towne',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SellaTurcicaXRayTomograph: Coding = {
  code: '42437-4',
  display: 'Sella turcica X-ray tomograph',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckXRayAPAndLateral: Coding = {
  code: '42438-2',
  display: 'Neck X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckXRayAPSingleView: Coding = {
  code: '42439-0',
  display: 'Neck X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckXRayMagnification: Coding = {
  code: '42441-6',
  display: 'Neck X-ray magnification',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRayLateralStanding: Coding = {
  code: '42442-4',
  display: 'Spine X-ray lateral standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRay3ViewsStanding: Coding = {
  code: '42443-2',
  display: 'Spine Thoracic X-ray 3 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayAPWRightBendingAndWLeftBendingAndWOBending: Coding = {
  code: '42444-0',
  display: 'Spine Thoracic X-ray AP W right bending and W left bending and WO bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayAPWLeftBendingAndWOBending: Coding = {
  code: '42445-7',
  display: 'Spine Thoracic X-ray AP W left bending and WO bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayAPWRightBendingAndWOBending: Coding = {
  code: '42446-5',
  display: 'Spine Thoracic X-ray AP W right bending and WO bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfCystOfThyroid: Coding = {
  code: '42447-3',
  display: 'US Guidance for aspiration of cyst of Thyroid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForExcisionalBiopsyOfBreast: Coding = {
  code: '42448-1',
  display: 'US Guidance for excisional biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfBreastLeft: Coding = {
  code: '42449-9',
  display: 'US Guidance for biopsy of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfCystOfBreastLeft: Coding = {
  code: '42450-7',
  display: 'US Guidance for aspiration of cyst of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisUSTransabdominalAndTransvaginal: Coding = {
  code: '42455-6',
  display: 'Pelvis US transabdominal and transvaginal',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForPlacementOfNeedleWireInBreast: Coding = {
  code: '42456-4',
  display: 'US Guidance for placement of needle wire in Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfBreastRight: Coding = {
  code: '42457-2',
  display: 'US Guidance for biopsy of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfCystOfBreastRight: Coding = {
  code: '42458-0',
  display: 'US Guidance for aspiration of cyst of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineUpperFluoroscopyWContrastPO: Coding = {
  code: '42459-8',
  display: 'Gastrointestine upper Fluoroscopy W contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SubmandibularGlandLeftFluoroscopyWContrastIntraSalivaryDuct: Coding = {
  code: '42460-6',
  display: 'Submandibular gland - left Fluoroscopy W contrast intra salivary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselGraftLeftUSDoppler: Coding = {
  code: '42461-4',
  display: 'Lower extremity vessel graft - left US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselGraftRightUSDoppler: Coding = {
  code: '42462-2',
  display: 'Lower extremity vessel graft - right US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfEndomyocardium: Coding = {
  code: '42463-0',
  display: 'US Guidance for biopsy of Endomyocardium',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgicalSpecimenUS: Coding = {
  code: '42468-9',
  display: 'Surgical specimen US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineUpperAndSmallBowelAndGallbladderFluoroscopyWContrastPO: Coding = {
  code: '42469-7',
  display: 'Gastrointestine upper and Small bowel and Gallbladder Fluoroscopy W contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineUpperAndGallbladderFluoroscopyWContrastPO: Coding = {
  code: '42470-5',
  display: 'Gastrointestine upper and Gallbladder Fluoroscopy W contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayStereo: Coding = {
  code: '42471-3',
  display: 'Pelvis X-ray stereo',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPInTraction: Coding = {
  code: '42472-1',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis AP in traction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayWatersStereo: Coding = {
  code: '42473-9',
  display: 'Sinuses X-ray Waters stereo',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayStereo: Coding = {
  code: '42474-7',
  display: 'Skull X-ray stereo',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselGraftLeftUSDoppler: Coding = {
  code: '42475-4',
  display: 'Upper extremity vessel graft - left US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselGraftRightUSDoppler: Coding = {
  code: '42476-2',
  display: 'Upper extremity vessel graft - right US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyVesselsTransplantUSDoppler: Coding = {
  code: '42477-0',
  display: 'Kidney vessels transplant US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfCystOfKidney: Coding = {
  code: '42478-8',
  display: 'US Guidance for drainage of cyst of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ContributingFactorCommunicationDocumentationMERSTH: Coding = {
  code: '42566-0',
  display: 'Contributing Factor communication/Documentation MERSTH',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogramXCCL: Coding = {
  code: '42680-9',
  display: 'Breast Mammogram XCCL',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColonFluoroscopyWGastrografinPR: Coding = {
  code: '42681-7',
  display: 'Colon Fluoroscopy W gastrografin PR',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineUpperFluoroscopyWBariumContrastPO: Coding = {
  code: '42683-3',
  display: 'Gastrointestine upper Fluoroscopy W barium contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastrointestineUpperFluoroscopyWGastrografinPO: Coding = {
  code: '42684-1',
  display: 'Gastrointestine upper Fluoroscopy W gastrografin PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipLeftXRay2Views: Coding = {
  code: '42685-8',
  display: 'Pelvis and Hip - left X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndHipRightXRay2Views: Coding = {
  code: '42686-6',
  display: 'Pelvis and Hip - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsBilateralXRay2Views: Coding = {
  code: '42687-4',
  display: 'Ribs - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAnestheticBlockInjectionOfSpine: Coding = {
  code: '42688-2',
  display: 'CT Guidance for anesthetic block injection of Spine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRayObliqueSingleView: Coding = {
  code: '42689-0',
  display: 'Spine X-ray oblique single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRayWFlexionAndWExtension: Coding = {
  code: '42690-8',
  display: 'Spine X-ray W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRay6Views: Coding = {
  code: '42691-6',
  display: 'Spine Cervical X-ray 6 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRay: Coding = {
  code: '42692-4',
  display: 'Spine Thoracic and Lumbar X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrinaryBladderAndUrethraMRICine: Coding = {
  code: '42693-2',
  display: 'Urinary Bladder and Urethra MRI cine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleMRIWContrastIV: Coding = {
  code: '42694-0',
  display: 'Clavicle MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegBilateralMRIWContrastIV: Coding = {
  code: '42695-7',
  display: 'Lower leg - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegBilateralMRI: Coding = {
  code: '42696-5',
  display: 'Lower leg - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegBilateralMRIWAndWOContrastIV: Coding = {
  code: '42697-3',
  display: 'Lower leg - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndThoracicAndLumbarMRI: Coding = {
  code: '42698-1',
  display: 'Spine Cervical and Thoracic and Lumbar MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenXRaySingleView: Coding = {
  code: '42699-9',
  display: 'Chest and Abdomen X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanWTc99mTaggedWBCIV: Coding = {
  code: '42700-5',
  display: 'Bone Scan W Tc-99m tagged WBC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForLocalizationOfPlacentaOfUterus: Coding = {
  code: '42701-3',
  display: 'CT Guidance for localization of placenta of Uterus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyGreaterThan1Hour: Coding = {
  code: '42702-1',
  display: 'Unspecified body region Fluoroscopy Greater than 1 hour',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyLessThan1Hour: Coding = {
  code: '42703-9',
  display: 'Unspecified body region Fluoroscopy Less than 1 hour',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAbscessDrainageOfAppendix: Coding = {
  code: '42705-4',
  display: 'US Guidance for abscess drainage of Appendix',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForInjectionOfPleuralSpace: Coding = {
  code: '42706-2',
  display: 'US Guidance for injection of Pleural space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartUSLimited: Coding = {
  code: '42707-0',
  display: 'Heart US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanWIn111TiuxetanIV: Coding = {
  code: '42708-8',
  display: 'Scan W In-111 tiuxetan IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverScanBloodPool: Coding = {
  code: '42709-6',
  display: 'Liver Scan blood pool',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayLimited: Coding = {
  code: '42710-4',
  display: 'Spine Cervical X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanWholeBodyWIn111TaggedWBCIV: Coding = {
  code: '42711-2',
  display: 'Scan whole body W In-111 tagged WBC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AVShuntScan: Coding = {
  code: '42776-5',
  display: 'AV shunt Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalTrialProtocolTrialName: Coding = {
  code: '42796-3',
  display: 'Clinical trial protocol Trial name',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayScaphoidSingleView: Coding = {
  code: '42811-0',
  display: 'Wrist - right X-ray scaphoid single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRayScaphoidSingleView: Coding = {
  code: '42812-8',
  display: 'Wrist X-ray scaphoid single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRayScaphoidSingleView: Coding = {
  code: '42813-6',
  display: 'Wrist - bilateral X-ray scaphoid single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayScaphoidSingleView: Coding = {
  code: '42814-4',
  display: 'Wrist - left X-ray scaphoid single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInUrine: Coding = {
  code: '42855-7',
  display: '11-Deoxycorticosterone [Mass/volume] in Urine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForPercutaneousDrainageOfAbscessOfCavity: Coding = {
  code: '43444-9',
  display: 'CT Guidance for percutaneous drainage of abscess of Cavity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonarySystemCT: Coding = {
  code: '43445-6',
  display: 'Pulmonary system CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTForTumorWholeBody: Coding = {
  code: '43446-4',
  display: 'CT for tumor whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForNeedleBiopsyOfBreastRight: Coding = {
  code: '43447-2',
  display: 'Mammogram Guidance for needle biopsy of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverMRIWAndWOFerumoxidesIV: Coding = {
  code: '43448-0',
  display: 'Liver MRI W and WO ferumoxides IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightMRIDynamicWContrastIV: Coding = {
  code: '43449-8',
  display: 'Ankle - right MRI dynamic W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftMRIDynamicWContrastIV: Coding = {
  code: '43450-6',
  display: 'Elbow - left MRI dynamic W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightMRIDynamicWContrastIV: Coding = {
  code: '43451-4',
  display: 'Elbow - right MRI dynamic W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftMRIDynamicWContrastIV: Coding = {
  code: '43452-2',
  display: 'Knee - left MRI dynamic W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightMRIDynamicWContrastIV: Coding = {
  code: '43453-0',
  display: 'Knee - right MRI dynamic W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonarySystemMRI: Coding = {
  code: '43454-8',
  display: 'Pulmonary system MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OropharynxMRI: Coding = {
  code: '43455-5',
  display: 'Oropharynx MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndSpineThoracicMRIWAndWOContrastIV: Coding = {
  code: '43456-3',
  display: 'Spine Cervical and Spine Thoracic MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndSpineThoracicMRI: Coding = {
  code: '43457-1',
  display: 'Spine Cervical and Spine Thoracic MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '43458-9',
  display: 'Orbit vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanDuringElectroconvulsiveShockTreatment: Coding = {
  code: '43459-7',
  display: 'Brain Scan during electroconvulsive shock treatment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralScanWFurosemideAndWRadionuclideIV: Coding = {
  code: '43461-3',
  display: 'Kidney - bilateral Scan W furosemide and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfBreastLeft: Coding = {
  code: '43462-1',
  display: 'US Guidance for needle biopsy of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenXRayAPSupineAndUprightAndPAChest: Coding = {
  code: '43463-9',
  display: 'Chest and Abdomen X-ray AP (supine and upright) and PA chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsBilateralAndChestXRayLateralAndPAChest: Coding = {
  code: '43464-7',
  display: 'Ribs - bilateral and Chest X-ray lateral and PA chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPRightLateralDecubitus: Coding = {
  code: '43466-2',
  display: 'Chest X-ray AP right lateral-decubitus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRay2ViewsAndRightObliqueAndLeftOblique: Coding = {
  code: '43467-0',
  display: 'Chest X-ray 2 views and right oblique and left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionXRay: Coding = {
  code: '43468-8',
  display: 'Unspecified body region X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionXRayOfForeignBody: Coding = {
  code: '43469-6',
  display: 'Unspecified body region X-ray of foreign body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayLE3Views: Coding = {
  code: '43470-4',
  display: 'Skull X-ray LE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy2Hour: Coding = {
  code: '43471-2',
  display: 'Unspecified body region Fluoroscopy 2 hour',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy90Minutes: Coding = {
  code: '43472-0',
  display: 'Unspecified body region Fluoroscopy 90 minutes',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct2HoursPostContrastRetrograde: Coding = {
  code: '43473-8',
  display: 'Fluoroscopy Guidance for endoscopy of Biliary ducts and Pancreatic duct-- 2 hours post contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct15MinutesPostContrastRetrograde: Coding = {
  code: '43474-6',
  display: 'Fluoroscopy Guidance for endoscopy of Biliary ducts and Pancreatic duct-- 15 minutes post contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct30MinutesPostContrastRetrograde: Coding = {
  code: '43475-3',
  display: 'Fluoroscopy Guidance for endoscopy of Biliary ducts and Pancreatic duct-- 30 minutes post contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct45MinutesPostContrastRetrograde: Coding = {
  code: '43476-1',
  display: 'Fluoroscopy Guidance for endoscopy of Biliary ducts and Pancreatic duct-- 45 minutes post contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct1HourPostContrastRetrograde: Coding = {
  code: '43477-9',
  display: 'Fluoroscopy Guidance for endoscopy of Biliary ducts and Pancreatic duct-- 1 hour post contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct15HoursPostContrastRetrograde: Coding = {
  code: '43478-7',
  display: 'Fluoroscopy Guidance for endoscopy of Biliary ducts and Pancreatic duct-- 1.5 hours post contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAbdominalFluoroscopicAngiogramRunoffWContrastIA: Coding = {
  code: '43479-5',
  display: 'Aorta abdominal Fluoroscopic angiogram runoff W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JointXRayLateralWManualStress: Coding = {
  code: '43480-3',
  display: 'Joint X-ray lateral W manual stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_JointXRayWFlexionAndWExtension: Coding = {
  code: '43481-1',
  display: 'Joint X-ray W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayGE3Views: Coding = {
  code: '43482-9',
  display: 'Knee - right X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRay3Or4Views: Coding = {
  code: '43483-7',
  display: 'Foot - right X-ray 3 or 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyXRayDuringSurgeryWContrastRetrograde: Coding = {
  code: '43485-2',
  display: 'Kidney X-ray during surgery W contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayGE3Views: Coding = {
  code: '43486-0',
  display: 'Sinuses X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForPlacementOfRadiationTherapyFieldsInUnspecifiedBodyRegion: Coding = {
  code: '43487-8',
  display: 'US Guidance for placement of radiation therapy fields in Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbLeftXRayGE3Views: Coding = {
  code: '43488-6',
  display: 'Thumb - left X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerSecondLeftXRayGE3Views: Coding = {
  code: '43489-4',
  display: 'Finger second - left X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerThirdLeftXRayGE3Views: Coding = {
  code: '43490-2',
  display: 'Finger third - left X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFourthLeftXRayGE3Views: Coding = {
  code: '43491-0',
  display: 'Finger fourth - left X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFifthLeftXRayGE3Views: Coding = {
  code: '43492-8',
  display: 'Finger fifth - left X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThumbRightXRayGE3Views: Coding = {
  code: '43493-6',
  display: 'Thumb - right X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerSecondRightXRayGE3Views: Coding = {
  code: '43494-4',
  display: 'Finger second - right X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerThirdRightXRayGE3Views: Coding = {
  code: '43495-1',
  display: 'Finger third - right X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFourthRightXRayGE3Views: Coding = {
  code: '43496-9',
  display: 'Finger fourth - right X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerFifthRightXRayGE3Views: Coding = {
  code: '43497-7',
  display: 'Finger fifth - right X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayGE3Views: Coding = {
  code: '43498-5',
  display: 'Knee - left X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRay3Or4Views: Coding = {
  code: '43499-3',
  display: 'Foot - left X-ray 3 or 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VesselScanFlow: Coding = {
  code: '43500-8',
  display: 'Vessel Scan flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VesselScanStatic: Coding = {
  code: '43501-6',
  display: 'Vessel Scan static',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAbscessDrainageOfSubphrenicSpace: Coding = {
  code: '43502-4',
  display: 'CT Guidance for abscess drainage of Subphrenic space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAndLowerExtremityVesselsCTAngiogramWContrastIV: Coding = {
  code: '43503-2',
  display: 'Aorta and Lower extremity vessels CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AxillaLeftMRIWContrastIV: Coding = {
  code: '43504-0',
  display: 'Axilla - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AxillaRightMRIWContrastIV: Coding = {
  code: '43505-7',
  display: 'Axilla - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OvaryBilateralMRI: Coding = {
  code: '43506-5',
  display: 'Ovary - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThymusGlandMRI: Coding = {
  code: '43507-3',
  display: 'Thymus gland MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AxillaLeftMRI: Coding = {
  code: '43508-1',
  display: 'Axilla - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AxillaLeftMRIWAndWOContrastIV: Coding = {
  code: '43509-9',
  display: 'Axilla - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AxillaRightMRI: Coding = {
  code: '43510-7',
  display: 'Axilla - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AxillaRightMRIWAndWOContrastIV: Coding = {
  code: '43511-5',
  display: 'Axilla - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegVesselsBilateralMRIAngiogramWContrastIV: Coding = {
  code: '43512-3',
  display: 'Lower leg vessels - bilateral MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegVesselsLeftMRIAngiogram: Coding = {
  code: '43513-1',
  display: 'Lower leg vessels - left MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighVesselsLeftMRIAngiogramWOContrast: Coding = {
  code: '43514-9',
  display: 'Thigh vessels - left MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThighVesselsRightMRIAngiogramWOContrast: Coding = {
  code: '43515-6',
  display: 'Thigh vessels - right MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristVesselsLeftMRIAngiogramWOContrast: Coding = {
  code: '43516-4',
  display: 'Wrist vessels - left MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristVesselsRightMRIAngiogramWOContrast: Coding = {
  code: '43517-2',
  display: 'Wrist vessels - right MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BonesXRaySurvey: Coding = {
  code: '43518-0',
  display: 'Bones X-ray survey',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BonesXRaySurveyLimited: Coding = {
  code: '43519-8',
  display: 'Bones X-ray survey limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleXRay1Or2Views: Coding = {
  code: '43521-4',
  display: 'Mandible X-ray 1 or 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRay1Or2Views: Coding = {
  code: '43522-2',
  display: 'Pelvis X-ray 1 or 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRay1Or2Views: Coding = {
  code: '43523-0',
  display: 'Sinuses X-ray 1 or 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayGE5Views: Coding = {
  code: '43524-8',
  display: 'Skull X-ray GE 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCTWOContrast: Coding = {
  code: '43525-5',
  display: 'Unspecified body region CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionSPECT: Coding = {
  code: '43526-3',
  display: 'Unspecified body region SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionCTDynamicWContrastIV: Coding = {
  code: '43527-1',
  display: 'Unspecified body region CT dynamic W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastUnilateralMRIWAndWOContrastIV: Coding = {
  code: '43528-9',
  display: 'Breast - unilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitPlusFacialBonesXRay: Coding = {
  code: '43529-7',
  display: 'Orbit + Facial bones X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitAndFaceAndNeckMRI: Coding = {
  code: '43530-5',
  display: 'Orbit and Face and Neck MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenXRayUprightAndPAChest: Coding = {
  code: '43532-1',
  display: 'Chest and Abdomen X-ray upright and PA chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleRightXRay: Coding = {
  code: '43533-9',
  display: 'Mandible - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleLeftXRay4Views: Coding = {
  code: '43534-7',
  display: 'Mandible - left X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleRightXRay4Views: Coding = {
  code: '43535-4',
  display: 'Mandible - right X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarFluoroscopyVideo: Coding = {
  code: '43536-2',
  display: 'Spine Lumbar Fluoroscopy video',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForDrainageOfUnspecifiedBodyRegion: Coding = {
  code: '43537-0',
  display: 'Fluoroscopy Guidance for drainage of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalFluoroscopyVideo: Coding = {
  code: '43538-8',
  display: 'Spine Cervical Fluoroscopy video',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRay2Or3Views: Coding = {
  code: '43539-6',
  display: 'Spine Cervical X-ray 2 or 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayGE3Views: Coding = {
  code: '43543-8',
  display: 'Pelvis X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanStaticAndFlow: Coding = {
  code: '43550-3',
  display: 'Brain Scan static and flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryUnilateralUS: Coding = {
  code: '43552-9',
  display: 'Carotid artery - unilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VesselsLeftFluoroscopicAngiogramWContrastIV: Coding = {
  code: '43554-5',
  display: 'vessels - left Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftMRIDynamicWContrastIV: Coding = {
  code: '43555-2',
  display: 'Ankle - left MRI dynamic W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegVesselsRightMRIAngiogram: Coding = {
  code: '43556-0',
  display: 'Lower leg vessels - right MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverAndBiliaryDuctsAndGallbladderScan: Coding = {
  code: '43557-8',
  display: 'Liver and Biliary ducts and Gallbladder Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForChangeOfDialysisCatheterInUnspecifiedBodyRegionWContrastIV: Coding = {
  code: '43558-6',
  display: 'Fluoroscopy Guidance for change of dialysis catheter in Unspecified body region-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrinaryBladderAndUrethraFluoroscopyWContrastIntraBladderDuringVoiding: Coding = {
  code: '43559-4',
  display: 'Urinary Bladder and Urethra Fluoroscopy W contrast intra bladder during voiding',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenXRayAPUprightAndAPChest: Coding = {
  code: '43561-0',
  display: 'Chest and Abdomen X-ray AP upright and AP chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkeletalSystemAxialScanBoneDensity: Coding = {
  code: '43562-8',
  display: 'Skeletal system.axial Scan Bone density',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkeletalSystemPeripheralScanBoneDensity: Coding = {
  code: '43563-6',
  display: 'Skeletal system.peripheral Scan Bone density',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfSuperficialMuscle: Coding = {
  code: '43564-4',
  display: 'US Guidance for biopsy of Superficial muscle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfDeepBone: Coding = {
  code: '43565-1',
  display: 'US Guidance for biopsy of Deep bone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipAndThighUS: Coding = {
  code: '43566-9',
  display: 'Hip and Thigh US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfDeepBone: Coding = {
  code: '43567-7',
  display: 'CT Guidance for biopsy of Deep bone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfDeepBone: Coding = {
  code: '43568-5',
  display: 'CT Guidance for needle biopsy of Deep bone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPUprightAndSupine: Coding = {
  code: '43569-3',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis AP upright and supine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRayPortable: Coding = {
  code: '43570-1',
  display: 'Hand X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfSoftBone: Coding = {
  code: '43571-9',
  display: 'CT Guidance for needle biopsy of Soft bone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalVesselsUSDopplerLimited: Coding = {
  code: '43572-7',
  display: 'Abdominal vessels US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperGastrointestineAndSmallBowelFluoroscopyWBariumContrastPO: Coding = {
  code: '43574-3',
  display: 'Upper Gastrointestine and Small bowel Fluoroscopy W barium contrast PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootSesamoidBonesLeftXRay: Coding = {
  code: '43641-0',
  display: 'Foot sesamoid bones - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanFlowWTc99mDTPAIV: Coding = {
  code: '43642-8',
  display: 'Brain Scan flow W Tc-99m DTPA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanFlowWTc99mGlucoheptonateIV: Coding = {
  code: '43643-6',
  display: 'Brain Scan flow W Tc-99m glucoheptonate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanFlowLimited: Coding = {
  code: '43644-4',
  display: 'Brain Scan flow limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanForInfarctQualitative: Coding = {
  code: '43645-1',
  display: 'Heart Scan for infarct qualitative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanForInfarctQualitativeAndQuantitative: Coding = {
  code: '43646-9',
  display: 'Heart Scan for infarct qualitative and quantitative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanForInfarctQuantitative: Coding = {
  code: '43647-7',
  display: 'Heart Scan for infarct quantitative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForEndocrineTumorMultipleAreasWI131MIBGIV: Coding = {
  code: '43648-5',
  display: 'Scan for endocrine tumor multiple areas W I-131 MIBG IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanForEndocrineTumorMultipleAreasWIn111PentetreotideIV: Coding = {
  code: '43649-3',
  display: 'Scan for endocrine tumor multiple areas W In-111 pentetreotide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverAndBiliaryDuctsAndGallbladderScanWCholecystokininAndWRadionuclideIV: Coding = {
  code: '43650-1',
  display: 'Liver and Biliary ducts and Gallbladder Scan W cholecystokinin and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverAndBiliaryDuctsAndGallbladderScanWSincalideAndWRadionuclideIV: Coding = {
  code: '43651-9',
  display: 'Liver and Biliary ducts and Gallbladder Scan W sincalide and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverAndSpleenSPECTFlow: Coding = {
  code: '43652-7',
  display: 'Liver and Spleen SPECT flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverAndSpleenScanFlow: Coding = {
  code: '43653-5',
  display: 'Liver and Spleen Scan flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverScanFlowWTc99mTaggedRBCIV: Coding = {
  code: '43654-3',
  display: 'Liver Scan flow W Tc-99m tagged RBC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverSPECTFlow: Coding = {
  code: '43655-0',
  display: 'Liver SPECT flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanPerfusionQuantitative: Coding = {
  code: '43656-8',
  display: 'Lung Scan perfusion quantitative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanQuantitative: Coding = {
  code: '43657-6',
  display: 'Lung Scan quantitative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionQuantitative: Coding = {
  code: '43658-4',
  display: 'Heart Scan perfusion quantitative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionQualitativeAtRestAndWRadionuclideIV: Coding = {
  code: '43659-2',
  display: 'Heart SPECT perfusion qualitative at rest and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionQualitativeAtRestAndWRadionuclideIV: Coding = {
  code: '43660-0',
  display: 'Heart Scan perfusion qualitative at rest and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionQuantitativeAtRestAndWRadionuclideIV: Coding = {
  code: '43661-8',
  display: 'Heart Scan perfusion quantitative at rest and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsSPECTFlowWTc99mGlucoheptonateIV: Coding = {
  code: '43662-6',
  display: 'Renal vessels SPECT flow W Tc-99m glucoheptonate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsScanFlowWTc99mGlucoheptonateIV: Coding = {
  code: '43663-4',
  display: 'Renal vessels Scan flow W Tc-99m glucoheptonate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsScanFlowWTc99mDTPAIV: Coding = {
  code: '43664-2',
  display: 'Renal vessels Scan flow W Tc-99m DTPA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsScanFlowWTc99mMertiatideIV: Coding = {
  code: '43665-9',
  display: 'Renal vessels Scan flow W Tc-99m Mertiatide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralAndRenalVesselsScanFlowWTc99mGlucoheptonateIV: Coding = {
  code: '43666-7',
  display: 'Kidney - bilateral and Renal vessels Scan flow W Tc-99m glucoheptonate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralAndRenalVesselsScanWTc99mDTPAIV: Coding = {
  code: '43667-5',
  display: 'Kidney - bilateral and Renal vessels Scan W Tc-99m DTPA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsScan: Coding = {
  code: '43669-1',
  display: 'Renal vessels Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpleenSPECTFlow: Coding = {
  code: '43670-9',
  display: 'Spleen SPECT flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanSpot: Coding = {
  code: '43671-7',
  display: 'Thyroid Scan spot',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanAndUptake: Coding = {
  code: '43672-5',
  display: 'Thyroid Scan and uptake',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidSPECTFlow: Coding = {
  code: '43673-3',
  display: 'Thyroid SPECT flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertDosageFormsAndStrengthsSection: Coding = {
  code: '43678-2',
  display: 'FDA package insert Dosage forms and strengths section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertMechanismOfActionSection: Coding = {
  code: '43679-0',
  display: 'FDA package insert Mechanism of action section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertNonclinicalToxicologySection: Coding = {
  code: '43680-8',
  display: 'FDA package insert Nonclinical toxicology section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPharmacodynamicsSection: Coding = {
  code: '43681-6',
  display: 'FDA package insert Pharmacodynamics section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPharmacokineticsSection: Coding = {
  code: '43682-4',
  display: 'FDA package insert Pharmacokinetics section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertRecentMajorChangesSection: Coding = {
  code: '43683-2',
  display: 'FDA package insert Recent major changes section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertUseInSpecificPopulationsSection: Coding = {
  code: '43684-0',
  display: 'FDA package insert Use in specific populations section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertWarningsAndPrecautionsSection: Coding = {
  code: '43685-7',
  display: 'FDA package insert Warnings and precautions section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfBreast: Coding = {
  code: '43756-6',
  display: 'US Guidance for aspiration of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfKidney: Coding = {
  code: '43757-4',
  display: 'CT Guidance for fine needle aspiration of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForLocalizationOfBreastLeft: Coding = {
  code: '43758-2',
  display: 'US Guidance for localization of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForLocalizationOfBreastBilateral: Coding = {
  code: '43759-0',
  display: 'US Guidance for localization of Breast - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForLocalizationOfBreastRight: Coding = {
  code: '43760-8',
  display: 'US Guidance for localization of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForThrombectomyOfVeinBilateralWContrastIV: Coding = {
  code: '43761-6',
  display: 'Fluoroscopic angiogram Guidance for thrombectomy of Vein - bilateral-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForThrombectomyOfVeinLeftWContrastIV: Coding = {
  code: '43762-4',
  display: 'Fluoroscopic angiogram Guidance for thrombectomy of Vein - left-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForThrombectomyOfVeinWContrastIV: Coding = {
  code: '43763-2',
  display: 'Fluoroscopic angiogram Guidance for thrombectomy of Vein-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForThrombectomyOfVeinRightWContrastIV: Coding = {
  code: '43764-0',
  display: 'Fluoroscopic angiogram Guidance for thrombectomy of Vein - right-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CarotidArteryBilateralUSDoppler: Coding = {
  code: '43765-7',
  display: 'Carotid artery - bilateral US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralCTWContrastIV: Coding = {
  code: '43766-5',
  display: 'Kidney - bilateral CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralCT: Coding = {
  code: '43767-3',
  display: 'Kidney - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyCTWAndWOContrastIV: Coding = {
  code: '43768-1',
  display: 'Kidney CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainAndInternalAuditoryCanalMRIWAndWOContrastIV: Coding = {
  code: '43769-9',
  display: 'Brain and Internal auditory canal MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyCTWOContrast: Coding = {
  code: '43770-7',
  display: 'Kidney CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVesselsUSDoppler: Coding = {
  code: '43771-5',
  display: 'Extremity vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainAndInternalAuditoryCanalMRI: Coding = {
  code: '43772-3',
  display: 'Brain and Internal auditory canal MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyMRIWOContrast: Coding = {
  code: '43773-1',
  display: 'Kidney MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralUS: Coding = {
  code: '43774-9',
  display: 'Kidney - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyMRIWAndWOContrastIV: Coding = {
  code: '43775-6',
  display: 'Kidney MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacArteryUSDopplerLimited: Coding = {
  code: '43776-4',
  display: 'Iliac artery US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanPerfusionAtRestAndWAdenosineAndWTl201IV: Coding = {
  code: '43777-2',
  display: 'Heart Scan perfusion at rest and W adenosine and W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayAPSupinePortable: Coding = {
  code: '43778-0',
  display: 'Chest X-ray AP supine portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRaySunrise: Coding = {
  code: '43779-8',
  display: 'Knee - left X-ray Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRaySunrise: Coding = {
  code: '43780-6',
  display: 'Knee X-ray Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicothoracicJunctionXRay: Coding = {
  code: '43781-4',
  display: 'Spine Cervicothoracic Junction X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '43782-2',
  display: 'Iliac artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVeinFluoroscopicAngiogramWContrastIVAndWReninSampling: Coding = {
  code: '43783-0',
  display: 'Renal vein Fluoroscopic angiogram W contrast IV and W renin sampling',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndThoracicAndLumbarXRay2Views: Coding = {
  code: '43784-8',
  display: 'Spine Cervical and Thoracic and Lumbar X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicothoracicJunctionXRayAPAndLateral: Coding = {
  code: '43785-5',
  display: 'Spine Cervicothoracic Junction X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullAndFacialBonesAndMandibleXRayForDentalMeasurement: Coding = {
  code: '43787-1',
  display: 'Skull and Facial bones and Mandible X-ray for dental measurement',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TubeFluoroscopyForPatencyWContrastViaTube: Coding = {
  code: '43788-9',
  display: 'Tube Fluoroscopy for patency W contrast via tube',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverAndBiliaryDuctsAndGallbladderScanForPatencyWTc99mIV: Coding = {
  code: '43789-7',
  display: 'Liver and Biliary ducts and Gallbladder Scan for patency W Tc-99m IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayGrasheyAndY: Coding = {
  code: '43790-5',
  display: 'Shoulder - right X-ray Grashey and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayOblique: Coding = {
  code: '43791-3',
  display: 'Spine Lumbar X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibioperonealArteriesRightFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '43792-1',
  display: 'Tibioperoneal arteries - right Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibioperonealArteriesFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '43793-9',
  display: 'Tibioperoneal arteries Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibioperonealArteriesBilateralFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '43794-7',
  display: 'Tibioperoneal arteries - bilateral Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibioperonealArteriesLeftFluoroscopicAngiogramAngioplastyWContrastIA: Coding = {
  code: '43795-4',
  display: 'Tibioperoneal arteries - left Fluoroscopic angiogram Angioplasty W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRayTunnelCarpal: Coding = {
  code: '43796-2',
  display: 'Wrist - bilateral X-ray tunnel.carpal',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfSuperficialLymphNode: Coding = {
  code: '43797-0',
  display: 'US Guidance for biopsy of Superficial lymph node',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAblationOfTissueOfLiver: Coding = {
  code: '44101-4',
  display: 'CT Guidance for ablation of tissue of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForProcedureOfJointSpace: Coding = {
  code: '44102-2',
  display: 'CT Guidance for procedure of Joint space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfLymphNode: Coding = {
  code: '44103-0',
  display: 'CT Guidance for fine needle aspiration of Lymph node',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfMediastinum: Coding = {
  code: '44104-8',
  display: 'CT Guidance for fine needle aspiration of Mediastinum',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfMuscle: Coding = {
  code: '44105-5',
  display: 'CT Guidance for fine needle aspiration of Muscle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfProstate: Coding = {
  code: '44106-3',
  display: 'CT Guidance for fine needle aspiration of Prostate',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfAbdomenRetroperitoneum: Coding = {
  code: '44107-1',
  display: 'CT Guidance for fine needle aspiration of Abdomen retroperitoneum',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfAdrenalGland: Coding = {
  code: '44108-9',
  display: 'CT Guidance for fine needle aspiration of Adrenal gland',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfDeepMuscle: Coding = {
  code: '44109-7',
  display: 'CT Guidance for biopsy of Deep muscle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleLocalizationOfBreast: Coding = {
  code: '44110-5',
  display: 'CT Guidance for needle localization of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullBaseCTWAndWOContrastIV: Coding = {
  code: '44111-3',
  display: 'Skull.base CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullBaseCTWOContrast: Coding = {
  code: '44112-1',
  display: 'Skull.base CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicCTWAndWOContrastIT: Coding = {
  code: '44113-9',
  display: 'Spine Thoracic CT W and WO contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarCTWAndWOContrastIT: Coding = {
  code: '44114-7',
  display: 'Spine Lumbar CT W and WO contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenAndPelvisCT: Coding = {
  code: '44115-4',
  display: 'Abdomen and Pelvis CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleCTLimited: Coding = {
  code: '44116-2',
  display: 'Mandible CT limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfAbdomenRetroperitoneum: Coding = {
  code: '44117-0',
  display: 'CT Guidance for biopsy of Abdomen retroperitoneum',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleLocalizationOfBreastWAndWOContrastIV: Coding = {
  code: '44118-8',
  display: 'CT Guidance for needle localization of Breast-- W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralCTWOContrast: Coding = {
  code: '44119-6',
  display: 'Breast - bilateral CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColonCT: Coding = {
  code: '44120-4',
  display: 'Colon CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForPercutaneousNeedleBiopsyOfBreast: Coding = {
  code: '44121-2',
  display: 'Mammogram Guidance for percutaneous needle biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceStereotacticForLocalizationInBrainWAndWOContrastIV: Coding = {
  code: '44122-0',
  display: 'MRI Guidance.stereotactic for localization in Brain-- W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BiliaryDuctsAndPancreaticDuctMRIWOContrast: Coding = {
  code: '44123-8',
  display: 'Biliary ducts and Pancreatic duct MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalGlandMRIWContrastIV: Coding = {
  code: '44124-6',
  display: 'Adrenal gland MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BiliaryDuctsAndPancreaticDuctMRIWContrastIV: Coding = {
  code: '44125-3',
  display: 'Biliary ducts and Pancreatic duct MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartMRICineForBloodFlowVelocityMappingWContrastIV: Coding = {
  code: '44126-1',
  display: 'Heart MRI cine for blood flow velocity mapping W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartMRILimitedCineForFunction: Coding = {
  code: '44127-9',
  display: 'Heart MRI limited cine for function',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '44128-7',
  display: 'Lower extremity vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsMRIAngiogramWOContrast: Coding = {
  code: '44129-5',
  display: 'Lower extremity vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchMRIAngiogramWOContrast: Coding = {
  code: '44130-3',
  display: 'Aortic arch MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaMRIAngiogramWAndWOContrastIV: Coding = {
  code: '44131-1',
  display: 'Aorta MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaMRIAngiogramWOContrast: Coding = {
  code: '44132-9',
  display: 'Aorta MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsMRIAngiogramWOContrast: Coding = {
  code: '44133-7',
  display: 'Renal vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '44134-5',
  display: 'Renal vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsBilateralMRIAngiogramWContrastIV: Coding = {
  code: '44135-2',
  display: 'Lower extremity vessels - bilateral MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionPET: Coding = {
  code: '44136-0',
  display: 'Unspecified body region PET',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartPET: Coding = {
  code: '44137-8',
  display: 'Heart PET',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainPET: Coding = {
  code: '44138-6',
  display: 'Brain PET',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PETWholeBody: Coding = {
  code: '44139-4',
  display: 'PET whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenAndPelvisScanForTumor: Coding = {
  code: '44140-2',
  display: 'Abdomen and Pelvis Scan for tumor',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverAndSpleenScanWTc99mMAAIV: Coding = {
  code: '44141-0',
  display: 'Liver and Spleen Scan W Tc-99m MAA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneScanWTc99mMedronateIV: Coding = {
  code: '44142-8',
  display: 'Bone Scan W Tc-99m medronate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanWTc99mTaggedRBCIV: Coding = {
  code: '44143-6',
  display: 'Heart Scan W Tc-99m tagged RBC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverScanWXe133Inhaled: Coding = {
  code: '44144-4',
  display: 'Liver Scan W Xe-133 inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParathyroidScanWTc99mSestamibiIV: Coding = {
  code: '44145-1',
  display: 'Parathyroid Scan W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneMarrowScanWTc99mSCIV: Coding = {
  code: '44146-9',
  display: 'Bone marrow Scan W Tc-99m SC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanAndUptakeWTc99mPertechnetateIV: Coding = {
  code: '44147-7',
  display: 'Thyroid Scan and uptake W Tc-99m pertechnetate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanFlowWTc99mBicisateIV: Coding = {
  code: '44148-5',
  display: 'Brain Scan flow W Tc-99m bicisate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeritoneovenousShuntScanForPatencyWTc99mMAAInj: Coding = {
  code: '44149-3',
  display: 'Peritoneovenous shunt Scan for patency W Tc-99m MAA inj',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainScanStaticWTc99mBicisateIV: Coding = {
  code: '44150-1',
  display: 'Brain Scan static W Tc-99m bicisate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTWTc99mSestamibiIV: Coding = {
  code: '44151-9',
  display: 'Heart SPECT W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainSPECTWTc99mBicisateIV: Coding = {
  code: '44152-7',
  display: 'Brain SPECT W Tc-99m bicisate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneySPECTWTc99mGlucoheptonateIV: Coding = {
  code: '44153-5',
  display: 'Kidney SPECT W Tc-99m glucoheptonate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTWDipyridamoleAndWTc99mSestamibiIV: Coding = {
  code: '44154-3',
  display: 'Heart SPECT W dipyridamole and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAblationOfTissueOfLiver: Coding = {
  code: '44155-0',
  display: 'US Guidance for ablation of tissue of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAblationOfTissueOfKidney: Coding = {
  code: '44156-8',
  display: 'US Guidance for ablation of tissue of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForFineNeedleAspirationOfPancreas: Coding = {
  code: '44157-6',
  display: 'US Guidance for fine needle aspiration of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForFineNeedleAspirationOfLiver: Coding = {
  code: '44158-4',
  display: 'US Guidance for fine needle aspiration of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForFineNeedleAspirationOfKidney: Coding = {
  code: '44159-2',
  display: 'US Guidance for fine needle aspiration of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForFineNeedleAspirationOfBreast: Coding = {
  code: '44160-0',
  display: 'US Guidance for fine needle aspiration of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfLung: Coding = {
  code: '44161-8',
  display: 'US Guidance for biopsy of Lung',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfAbdomenRetroperitoneum: Coding = {
  code: '44162-6',
  display: 'US Guidance for biopsy of Abdomen retroperitoneum',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicOutletUS: Coding = {
  code: '44163-4',
  display: 'Thoracic outlet US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadAndNeckUS: Coding = {
  code: '44164-2',
  display: 'Head and Neck US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionUSAnd3DReconstruction: Coding = {
  code: '44165-9',
  display: 'Unspecified body region US and 3D reconstruction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAbscessDrainageOfSubphrenicSpace: Coding = {
  code: '44166-7',
  display: 'US Guidance for abscess drainage of Subphrenic space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAbscessDrainageOfKidney: Coding = {
  code: '44167-5',
  display: 'US Guidance for abscess drainage of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAbscessDrainageOfPelvis: Coding = {
  code: '44168-3',
  display: 'US Guidance for abscess drainage of Pelvis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAbscessDrainageOfPeritonealSpace: Coding = {
  code: '44169-1',
  display: 'US Guidance for abscess drainage of Peritoneal space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfLiver: Coding = {
  code: '44170-9',
  display: 'US Guidance for needle biopsy of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfChestPleura: Coding = {
  code: '44171-7',
  display: 'US Guidance for needle biopsy of Chest.pleura',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForDrainageOfPancreas: Coding = {
  code: '44172-5',
  display: 'US Guidance for drainage of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeripheralArteryUSLimited: Coding = {
  code: '44173-3',
  display: 'Peripheral artery US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsUSDoppler: Coding = {
  code: '44174-1',
  display: 'Lower extremity vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckVesselsUSDoppler: Coding = {
  code: '44175-8',
  display: 'Neck vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRaySingleViewPortable: Coding = {
  code: '44176-6',
  display: 'Hip X-ray Single view portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityBilateralXRayAPSingleViewStanding: Coding = {
  code: '44177-4',
  display: 'Lower extremity - bilateral X-ray AP single view standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayObliqueViewAndViewsWRightBendingAndWLeftBending: Coding = {
  code: '44178-2',
  display: 'Spine Lumbar X-ray oblique view and (views W right bending and W left bending)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumAndCoccyxXRay2Views: Coding = {
  code: '44179-0',
  display: 'Sacrum and Coccyx X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointXRay2Or3Views: Coding = {
  code: '44181-6',
  display: 'Sacroiliac Joint X-ray 2 or 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRay2ViewsPortable: Coding = {
  code: '44182-4',
  display: 'Hand X-ray 2 views portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusAndUlnaXRay2ViewsPortable: Coding = {
  code: '44183-2',
  display: 'Radius and Ulna X-ray 2 views portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowXRay2ViewsPortable: Coding = {
  code: '44184-0',
  display: 'Elbow X-ray 2 views portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurXRayAPAndLateralPortable: Coding = {
  code: '44185-7',
  display: 'Femur X-ray AP and lateral portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayAPAndLateralPortable: Coding = {
  code: '44186-5',
  display: 'Foot X-ray AP and lateral portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayAPAndObliqueAndOdontoidAndLateralPortableWFlexionAndWExtension: Coding = {
  code: '44187-3',
  display: 'Spine Cervical X-ray AP and oblique and odontoid and lateral portable W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootXRayGE3Views: Coding = {
  code: '44188-1',
  display: 'Foot X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointXRayGE3Views: Coding = {
  code: '44189-9',
  display: 'Sacroiliac Joint X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRayGE3Views: Coding = {
  code: '44190-7',
  display: 'Wrist X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsAndChestXRayGE3AndPAChestViews: Coding = {
  code: '44191-5',
  display: 'Ribs and Chest X-ray GE 3 and PA Chest views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRayGE3PortableViews: Coding = {
  code: '44192-3',
  display: 'Pelvis X-ray GE 3 Portable views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRayGE3PortableViews: Coding = {
  code: '44193-1',
  display: 'Hand X-ray GE 3 Portable views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRayGE4ViewsWRightBendingAndWLeftBending: Coding = {
  code: '44194-9',
  display: 'Spine X-ray GE 4 views W right bending and W left bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRayGE5Views: Coding = {
  code: '44195-6',
  display: 'Knee X-ray GE 5 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayGE5ViewsWRightBendingAndWLeftBending: Coding = {
  code: '44196-4',
  display: 'Spine Lumbar X-ray GE 5 views W right bending and W left bending',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayGE5ViewsStanding: Coding = {
  code: '44197-2',
  display: 'Knee - bilateral X-ray GE 5 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRay1Or2Views: Coding = {
  code: '44198-0',
  display: 'Knee X-ray 1 or 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesXRay1Or2Views: Coding = {
  code: '44199-8',
  display: 'Facial bones X-ray 1 or 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisXRay1Or2ViewsPortable: Coding = {
  code: '44201-2',
  display: 'Pelvis X-ray 1 or 2 views portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRay1Or2ViewsPortable: Coding = {
  code: '44202-0',
  display: 'Knee X-ray 1 or 2 views portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalAndThoracicAndLumbarXRayPortable: Coding = {
  code: '44203-8',
  display: 'Spine Cervical and Thoracic and Lumbar X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousNeedleBiopsyOfLung: Coding = {
  code: '44204-6',
  display: 'Fluoroscopy Guidance for percutaneous needle biopsy of Lung',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityBilateralXRayStanding: Coding = {
  code: '44205-3',
  display: 'Lower extremity - bilateral X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisSingleView: Coding = {
  code: '44206-1',
  display: 'Spine Thoracic and Lumbar X-ray scoliosis single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitXRayForForeignBody: Coding = {
  code: '44208-7',
  display: 'Orbit X-ray for foreign body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesXRayLimited: Coding = {
  code: '44209-5',
  display: 'Sinuses X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleXRayGE3Views: Coding = {
  code: '44210-3',
  display: 'Ankle X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayGE4Views: Coding = {
  code: '44211-1',
  display: 'Chest X-ray GE 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRayGE4Views: Coding = {
  code: '44212-9',
  display: 'Spine Cervical X-ray GE 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfPancreaticDuctWContrastRetrograde: Coding = {
  code: '44213-7',
  display: 'Fluoroscopy Guidance for endoscopy of Pancreatic duct-- W contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsWContrastRetrograde: Coding = {
  code: '44214-5',
  display: 'Fluoroscopy Guidance for endoscopy of Biliary ducts-- W contrast retrograde',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfUnspecifiedBodyRegion: Coding = {
  code: '44215-2',
  display: 'Fluoroscopy Guidance for fine needle aspiration of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfThyroid: Coding = {
  code: '44216-0',
  display: 'Fluoroscopy Guidance for fine needle aspiration of Thyroid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfKidney: Coding = {
  code: '44217-8',
  display: 'Fluoroscopy Guidance for fine needle aspiration of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfPancreas: Coding = {
  code: '44218-6',
  display: 'Fluoroscopy Guidance for fine needle aspiration of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfLymphNode: Coding = {
  code: '44219-4',
  display: 'Fluoroscopy Guidance for fine needle aspiration of Lymph node',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfLiver: Coding = {
  code: '44220-2',
  display: 'Fluoroscopy Guidance for fine needle aspiration of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfDeepTissue: Coding = {
  code: '44221-0',
  display: 'Fluoroscopy Guidance for fine needle aspiration of Deep tissue',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForProcedureOfJointSpace: Coding = {
  code: '44222-8',
  display: 'Fluoroscopy Guidance for procedure of Joint space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfOvary: Coding = {
  code: '44223-6',
  display: 'Fluoroscopy Guidance for percutaneous drainage of abscess of Ovary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPlacementOfTubeInUnspecifiedBodyRegion: Coding = {
  code: '44224-4',
  display: 'Fluoroscopy Guidance for placement of tube in Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfLiverWContrastIV: Coding = {
  code: '44225-1',
  display: 'Fluoroscopy Guidance for needle biopsy of Liver-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColonFluoroscopyReductionWViewsWBariumContrastPR: Coding = {
  code: '44226-9',
  display: 'Colon Fluoroscopy Reduction W views W barium contrast PR',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColonFluoroscopyWBariumContrastPR: Coding = {
  code: '44227-7',
  display: 'Colon Fluoroscopy W barium contrast PR',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAblationOfTissueOfKidney: Coding = {
  code: '44228-5',
  display: 'CT Guidance for ablation of tissue of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BonesCT: Coding = {
  code: '44229-3',
  display: 'Bones CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SuperiorMesentericVesselsMRIAngiogramWOContrast: Coding = {
  code: '44230-1',
  display: 'Superior mesenteric vessels MRI angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SuperiorMesentericVesselsMRIAngiogramWAndWOContrastIV: Coding = {
  code: '44231-9',
  display: 'Superior mesenteric vessels MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralScanWAndWOTc99mMertiatideIV: Coding = {
  code: '44232-7',
  display: 'Kidney - bilateral Scan W and WO Tc-99m Mertiatide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralScanWAndWOTc99mDTPAIV: Coding = {
  code: '44233-5',
  display: 'Kidney - bilateral Scan W and WO Tc-99m DTPA IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralScanWTc99mGlucoheptonateIV: Coding = {
  code: '44234-3',
  display: 'Kidney - bilateral Scan W Tc-99m glucoheptonate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SuperiorMesentericVesselsUSDoppler: Coding = {
  code: '44235-0',
  display: 'Superior mesenteric vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselGraftBilateralUSDoppler: Coding = {
  code: '44236-8',
  display: 'Upper extremity vessel graft - bilateral US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselGraftBilateralUSDopplerLimited: Coding = {
  code: '44237-6',
  display: 'Upper extremity vessel graft - bilateral US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TracheaXRay: Coding = {
  code: '44238-4',
  display: 'Trachea X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsUnilateralAndChestXRayGe3AndPAChestPortableViews: Coding = {
  code: '44239-2',
  display: 'Ribs - unilateral and Chest X-ray Ge 3 and PA Chest Portable views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeripheralArteriesBilateralFluoroscopicAngiogramWContrastIA: Coding = {
  code: '44240-0',
  display: 'Peripheral arteries - bilateral Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertStorageAndHandlingSection: Coding = {
  code: '44425-7',
  display: 'FDA package insert Storage and handling section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_Progesterone11DeoxycorticosteroneMassRatioInSerumOrPlasma: Coding = {
  code: '44729-2',
  display: 'Progesterone/11-Deoxycorticosterone [Mass Ratio] in Serum or Plasma',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SelfManagement: Coding = {
  code: '44943-9',
  display: 'Self management',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NursingNotes: Coding = {
  code: '46208-5',
  display: 'Nursing notes',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProviderOrders: Coding = {
  code: '46209-3',
  display: 'Provider orders',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CaseManagerNote: Coding = {
  code: '46210-1',
  display: 'Case manager Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TiltTableStudy: Coding = {
  code: '46213-5',
  display: 'Tilt table study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IntracardiacAblationStudy: Coding = {
  code: '46214-3',
  display: 'Intracardiac ablation study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WoundCareManagementNote: Coding = {
  code: '46215-0',
  display: 'Wound care management Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FetalDocumentVitalSignsMeasurements: Coding = {
  code: '46242-4',
  display: 'Fetal Document Vital signs measurements',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HistoryOfMedicalDeviceUse: Coding = {
  code: '46264-8',
  display: 'History of medical device use',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAspirationOrInjectionOfCystOfUnspecifiedBodyRegion: Coding = {
  code: '46281-2',
  display: 'CT Guidance for aspiration or injection of cyst of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOrInjectionOfCystOfUnspecifiedBodyRegion: Coding = {
  code: '46282-0',
  display: 'US Guidance for aspiration or injection of cyst of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForFineNeedleAspirationOfBreastRight: Coding = {
  code: '46283-8',
  display: 'Mammogram Guidance for fine needle aspiration of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForFineNeedleAspirationOfBreastLeft: Coding = {
  code: '46284-6',
  display: 'Mammogram Guidance for fine needle aspiration of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForCoreNeedleBiopsyOfThyroid: Coding = {
  code: '46285-3',
  display: 'US Guidance for core needle biopsy of Thyroid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForNeedleBiopsyOfBreast: Coding = {
  code: '46286-1',
  display: 'Mammogram Guidance for needle biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfUnspecifiedBodyRegion: Coding = {
  code: '46287-9',
  display: 'CT Guidance for needle biopsy of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfProstate: Coding = {
  code: '46288-7',
  display: 'US Guidance for needle biopsy of Prostate',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfUnspecifiedBodyRegionWAndWOContrastIV: Coding = {
  code: '46289-5',
  display: 'CT Guidance for biopsy of Unspecified body region-- W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfUnspecifiedBodyRegionWOContrast: Coding = {
  code: '46290-3',
  display: 'CT Guidance for biopsy of Unspecified body region-- WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfUnspecifiedBodyRegionWAndWOContrastIV: Coding = {
  code: '46291-1',
  display: 'CT Guidance for drainage of Unspecified body region-- W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfUnspecifiedBodyRegionWContrastIV: Coding = {
  code: '46292-9',
  display: 'CT Guidance for drainage of Unspecified body region-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForDrainageOfUnspecifiedBodyRegionWOContrast: Coding = {
  code: '46293-7',
  display: 'CT Guidance for drainage of Unspecified body region-- WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForReplacementOfPercutaneousDrainageTubeInStomach: Coding = {
  code: '46294-5',
  display: 'Fluoroscopy Guidance for replacement of percutaneous drainage tube in Stomach',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceStereotacticForCoreNeedleBiopsyOfBreastLeft: Coding = {
  code: '46295-2',
  display: 'Mammogram Guidance.stereotactic for core needle biopsy of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceStereotacticForCoreNeedleBiopsyOfBreast: Coding = {
  code: '46296-0',
  display: 'Mammogram Guidance.stereotactic for core needle biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECT: Coding = {
  code: '46297-8',
  display: 'SPECT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidBilateralCT: Coding = {
  code: '46298-6',
  display: 'Mastoid - bilateral CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastUnilateralMRI: Coding = {
  code: '46299-4',
  display: 'Breast - unilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesCTCoronal: Coding = {
  code: '46300-0',
  display: 'Sinuses CT coronal',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinBilateralUSDopplerLimited: Coding = {
  code: '46301-8',
  display: 'Extremity vein - bilateral US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteryBilateralUSDopplerLimited: Coding = {
  code: '46302-6',
  display: 'Upper extremity artery - bilateral US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsUSDopplerLimited: Coding = {
  code: '46303-4',
  display: 'Upper extremity vessels US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SinusesCTLimitedWOContrast: Coding = {
  code: '46304-2',
  display: 'Sinuses CT limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTWholeBody: Coding = {
  code: '46305-9',
  display: 'CT whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTWholeBodyWContrastIV: Coding = {
  code: '46306-7',
  display: 'CT whole body W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsRightCTAngiogramWAndWOContrastIV: Coding = {
  code: '46307-5',
  display: 'Lower extremity vessels - right CT angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsLeftCTAngiogramWAndWOContrastIV: Coding = {
  code: '46308-3',
  display: 'Lower extremity vessels - left CT angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsRightCTAngiogramWAndWOContrastIV: Coding = {
  code: '46309-1',
  display: 'Upper extremity vessels - right CT angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitAndFaceAndNeckMRIWAndWOContrastIV: Coding = {
  code: '46310-9',
  display: 'Orbit and Face and Neck MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandCTWAndWOContrastIV: Coding = {
  code: '46311-7',
  display: 'Parotid gland CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsLeftCTAngiogramWAndWOContrastIV: Coding = {
  code: '46312-5',
  display: 'Upper extremity vessels - left CT angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisCTWAndWOReducedContrastVolumeIV: Coding = {
  code: '46313-3',
  display: 'Pelvis CT W and WO reduced contrast volume IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalCTWAndWOReducedContrastVolumeIV: Coding = {
  code: '46314-1',
  display: 'Internal auditory canal CT W and WO reduced contrast volume IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesAndMaxillaCTWAndWOReducedContrastVolumeIV: Coding = {
  code: '46315-8',
  display: 'Facial bones and Maxilla CT W and WO reduced contrast volume IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCTWAndWOReducedContrastVolumeIV: Coding = {
  code: '46316-6',
  display: 'Head CT W and WO reduced contrast volume IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestCTWAndWOReducedContrastVolumeIV: Coding = {
  code: '46317-4',
  display: 'Chest CT W and WO reduced contrast volume IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenCTWAndWOReducedContrastVolumeIV: Coding = {
  code: '46318-2',
  display: 'Abdomen CT W and WO reduced contrast volume IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowMRIWContrastIntraarticular: Coding = {
  code: '46319-0',
  display: 'Elbow MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitAndFaceCTWContrastIV: Coding = {
  code: '46320-8',
  display: 'Orbit and Face CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitAndFaceAndNeckMRIWContrastIV: Coding = {
  code: '46321-6',
  display: 'Orbit and Face and Neck MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyCTWContrastIV: Coding = {
  code: '46322-4',
  display: 'Kidney CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastUnilateralMRIWContrastIV: Coding = {
  code: '46323-2',
  display: 'Breast - unilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVesselsMRIAngiogramWContrastIV: Coding = {
  code: '46324-0',
  display: 'Lower extremity vessels MRI angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InternalAuditoryCanalCTWReducedContrastVolumeIV: Coding = {
  code: '46325-7',
  display: 'Internal auditory canal CT W reduced contrast volume IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesAndMaxillaCTWReducedContrastVolumeIV: Coding = {
  code: '46326-5',
  display: 'Facial bones and Maxilla CT W reduced contrast volume IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestCTWReducedContrastVolumeIV: Coding = {
  code: '46327-3',
  display: 'Chest CT W reduced contrast volume IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCTWReducedContrastVolumeIV: Coding = {
  code: '46328-1',
  display: 'Head CT W reduced contrast volume IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisCTWReducedContrastVolumeIV: Coding = {
  code: '46329-9',
  display: 'Pelvis CT W reduced contrast volume IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenCTWReducedContrastVolumeIV: Coding = {
  code: '46330-7',
  display: 'Abdomen CT W reduced contrast volume IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitCTWOContrast: Coding = {
  code: '46331-5',
  display: 'Orbit CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitAndFaceAndNeckMRIWOContrast: Coding = {
  code: '46332-3',
  display: 'Orbit and Face and Neck MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastUnilateralMRIWOContrast: Coding = {
  code: '46333-1',
  display: 'Breast - unilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralMammogramSingleView: Coding = {
  code: '46335-6',
  display: 'Breast - bilateral Mammogram Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftMammogramSingleView: Coding = {
  code: '46336-4',
  display: 'Breast - left Mammogram Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightMammogramSingleView: Coding = {
  code: '46337-2',
  display: 'Breast - right Mammogram Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastUnilateralMammogramSingleView: Coding = {
  code: '46338-0',
  display: 'Breast - unilateral Mammogram Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastUnilateralMammogram: Coding = {
  code: '46339-8',
  display: 'Breast - unilateral Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbosacralJunctionXRay: Coding = {
  code: '46340-6',
  display: 'Spine Lumbosacral Junction X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenFluoroscopy: Coding = {
  code: '46341-4',
  display: 'Abdomen Fluoroscopy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastFFDMammogram: Coding = {
  code: '46342-2',
  display: 'Breast FFD mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayGE3Views: Coding = {
  code: '46343-0',
  display: 'Wrist - right X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRayGE3Views: Coding = {
  code: '46344-8',
  display: 'Elbow - left X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRayGE3Views: Coding = {
  code: '46345-5',
  display: 'Elbow - right X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayGE3Views: Coding = {
  code: '46346-3',
  display: 'Wrist - left X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRayGE3Views: Coding = {
  code: '46347-1',
  display: 'Ankle - right X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayGE2AndPAAndLateralViews: Coding = {
  code: '46348-9',
  display: 'Chest X-ray GE 2 and PA and Lateral views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralXRayAPAndTransthoracic: Coding = {
  code: '46349-7',
  display: 'Shoulder - bilateral X-ray AP and transthoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastUnilateralMammogramDiagnostic: Coding = {
  code: '46350-5',
  display: 'Breast - unilateral Mammogram diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantBilateralMammogramDisplacement: Coding = {
  code: '46351-3',
  display: 'Breast implant - bilateral Mammogram displacement',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastDuctMammogramDuringSurgeryWContrastIntraDuct: Coding = {
  code: '46352-1',
  display: 'Breast duct Mammogram during surgery W contrast intra duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightFFDMammogramScreening: Coding = {
  code: '46354-7',
  display: 'Breast - right FFD mammogram screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftFFDMammogramScreening: Coding = {
  code: '46355-4',
  display: 'Breast - left FFD mammogram screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastUnilateralMammogramScreening: Coding = {
  code: '46356-2',
  display: 'Breast - unilateral Mammogram screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ColonFluoroscopyWAirContrastPR: Coding = {
  code: '46357-0',
  display: 'Colon Fluoroscopy W air contrast PR',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIWholeBody: Coding = {
  code: '46358-8',
  display: 'MRI whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SuperiorMesentericVesselsMRIAngiogram: Coding = {
  code: '46359-6',
  display: 'Superior mesenteric vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchMRIAngiogramWAndWOContrastIV: Coding = {
  code: '46360-4',
  display: 'Aortic arch MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungScanVentilationWXe133Inhaled: Coding = {
  code: '46361-2',
  display: 'Lung Scan ventilation W Xe-133 inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootVesselsUSDoppler: Coding = {
  code: '46362-0',
  display: 'Foot vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinUS: Coding = {
  code: '46363-8',
  display: 'Lower extremity vein US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinBilateralUS: Coding = {
  code: '46364-6',
  display: 'Lower extremity vein - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAblationOfTissueOfCeliacPlexus: Coding = {
  code: '46365-3',
  display: 'CT Guidance for ablation of tissue of Celiac plexus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTGuidanceForBiopsyOfBone: Coding = {
  code: '46366-1',
  display: 'SPECT Guidance for biopsy of Bone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfAdrenalGland: Coding = {
  code: '46367-9',
  display: 'CT Guidance for needle biopsy of Adrenal gland',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfBreast: Coding = {
  code: '46368-7',
  display: 'CT Guidance for needle biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfOvary: Coding = {
  code: '46369-5',
  display: 'US Guidance for needle biopsy of Ovary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfPelvis: Coding = {
  code: '46370-3',
  display: 'US Guidance for needle biopsy of Pelvis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_XRayGuidanceForChangeOfPercutaneousTubeInUnspecifiedBodyRegionWContrast: Coding = {
  code: '46371-1',
  display: 'X-ray Guidance for change of percutaneous tube in Unspecified body region-- W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfBiliaryDucts: Coding = {
  code: '46372-9',
  display: 'Fluoroscopy Guidance for percutaneous drainage of Biliary ducts',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTGuidanceForPlacementOfTubeInChest: Coding = {
  code: '46373-7',
  display: 'SPECT Guidance for placement of tube in Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CerebralArteryUS: Coding = {
  code: '46374-5',
  display: 'Cerebral artery US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ArteryUS: Coding = {
  code: '46375-2',
  display: 'Artery US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralFluoroscopyWContrastAntegrade: Coding = {
  code: '46376-0',
  display: 'Kidney - bilateral Fluoroscopy W contrast antegrade',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayGE3Views: Coding = {
  code: '46377-8',
  display: 'Skull X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayPAStandingAndWFlexion: Coding = {
  code: '46378-6',
  display: 'Knee - bilateral X-ray PA standing and W flexion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselsBilateralUSDoppler: Coding = {
  code: '46379-4',
  display: 'Upper extremity vessels - bilateral US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantUnilateralMammogram: Coding = {
  code: '46380-2',
  display: 'Breast Implant - unilateral Mammogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowPlusRadiusPlusUlnaXRay: Coding = {
  code: '46381-0',
  display: 'Elbow+Radius+Ulna X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandVesselsUSDoppler: Coding = {
  code: '46382-8',
  display: 'Hand vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTGuidanceForBiopsyOfSuperficialBone: Coding = {
  code: '46384-4',
  display: 'SPECT Guidance for biopsy of Superficial bone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVesselGraftUSDoppler: Coding = {
  code: '46385-1',
  display: 'Upper extremity vessel graft US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TeethXRayBitewing: Coding = {
  code: '46386-9',
  display: 'Teeth X-ray bitewing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForFineNeedleAspirationOfBreast: Coding = {
  code: '46387-7',
  display: 'Mammogram Guidance for fine needle aspiration of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaUSDoppler: Coding = {
  code: '46388-5',
  display: 'Aorta US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralXRayAndRadialHeadCapitellar: Coding = {
  code: '46389-3',
  display: 'Elbow - bilateral X-ray and radial head capitellar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayGE3Views: Coding = {
  code: '46390-1',
  display: 'Ankle - left X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayPortable: Coding = {
  code: '46391-9',
  display: 'Shoulder X-ray portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSinuses: Coding = {
  code: '46392-7',
  display: 'Fluoroscopy Guidance for injection of Sinuses',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverCTWXe133Inhaled: Coding = {
  code: '46393-5',
  display: 'Liver CT W Xe-133 inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCTDynamicWContrastIV: Coding = {
  code: '46394-3',
  display: 'Head CT dynamic W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTGatedAndEjectionFractionAtRestAndWStressAndWRadionuclideIV: Coding = {
  code: '46395-0',
  display: 'Heart SPECT gated and ejection fraction at rest and W stress and W radionuclide IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTGatedAtRestAndWTc99mSestamibiIV: Coding = {
  code: '46396-8',
  display: 'Heart SPECT gated at rest and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalAdmissionHistoryAndPhysicalNote: Coding = {
  code: '47039-3',
  display: 'Hospital Admission history and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_Consultation2ndOpinion: Coding = {
  code: '47040-1',
  display: 'Consultation 2nd opinion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalConsultation2ndOpinion: Coding = {
  code: '47041-9',
  display: 'Hospital Consultation 2nd opinion',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CounselingNote: Coding = {
  code: '47042-7',
  display: 'Counseling note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GroupCounselingNote: Coding = {
  code: '47043-5',
  display: 'Group counseling note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalInitialAssessmentNote: Coding = {
  code: '47044-3',
  display: 'Hospital Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_StudyReport: Coding = {
  code: '47045-0',
  display: 'Study report',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SummaryOfDeathNote: Coding = {
  code: '47046-8',
  display: 'Summary of death note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SupervisoryNote: Coding = {
  code: '47047-6',
  display: 'Supervisory note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DiagnosticInterventionalStudyReportInterventionalRadiology: Coding = {
  code: '47048-4',
  display: 'Diagnostic interventional study report Interventional radiology',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HIVTreatmentFormDocument: Coding = {
  code: '47245-6',
  display: 'HIV treatment form Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestCTLimitedWOContrast: Coding = {
  code: '47366-0',
  display: 'Chest CT limited WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestFluoroscopyGE4Views: Coding = {
  code: '47367-8',
  display: 'Chest Fluoroscopy GE 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestXRayGE4AndPaAndLateralViews: Coding = {
  code: '47368-6',
  display: 'Chest X-ray GE 4 and Pa and Lateral views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRayGE3Views: Coding = {
  code: '47370-2',
  display: 'Hand - left X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRayGE3Views: Coding = {
  code: '47371-0',
  display: 'Hand - right X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayDuringSurgery: Coding = {
  code: '47372-8',
  display: 'Hip X-ray during surgery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay1Or2Views: Coding = {
  code: '47373-6',
  display: 'Knee - left X-ray 1 or 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayGE4Views: Coding = {
  code: '47374-4',
  display: 'Knee - left X-ray GE 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRay1Or2Views: Coding = {
  code: '47375-1',
  display: 'Knee - right X-ray 1 or 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayGE4Views: Coding = {
  code: '47376-9',
  display: 'Knee - right X-ray GE 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayLE4Views: Coding = {
  code: '47377-7',
  display: 'Knee - right X-ray LE 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverSPECTBloodPool: Coding = {
  code: '47378-5',
  display: 'Liver SPECT blood pool',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleXRayGE4Views: Coding = {
  code: '47379-3',
  display: 'Mandible X-ray GE 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleXRayLE3Views: Coding = {
  code: '47380-1',
  display: 'Mandible X-ray LE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidXRayGE3Views: Coding = {
  code: '47381-9',
  display: 'Mastoid X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRayGE4Views: Coding = {
  code: '47382-7',
  display: 'Spine Lumbar X-ray GE 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FunctionalStatusAssessmentNote: Coding = {
  code: '47420-5',
  display: 'Functional status assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HistoryOfProceduresDocument: Coding = {
  code: '47519-4',
  display: 'History of Procedures Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfSputumCytoStain: Coding = {
  code: '47520-2',
  display: 'Cytology report of Sputum Cyto stain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfBreastFineNeedleAspirateCytoStain: Coding = {
  code: '47521-0',
  display: 'Cytology report of Breast fine needle aspirate Cyto stain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfNippleDischargeCytoStain: Coding = {
  code: '47522-8',
  display: 'Cytology report of Nipple discharge Cyto stain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfBodyFluidCytoStain: Coding = {
  code: '47523-6',
  display: 'Cytology report of Body fluid Cyto stain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfThyroidFineNeedleAspirateCytoStain: Coding = {
  code: '47524-4',
  display: 'Cytology report of Thyroid fine needle aspirate Cyto stain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfUrineCytoStain: Coding = {
  code: '47525-1',
  display: 'Cytology report of Urine Cyto stain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfUnspecifiedSpecimenCytoStain: Coding = {
  code: '47526-9',
  display: 'Cytology report of Unspecified specimen Cyto stain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfCervicalOrVaginalSmearOrScrapingCytoStainThinPrep: Coding = {
  code: '47527-7',
  display: 'Cytology report of Cervical or vaginal smear or scraping Cyto stain.thin prep',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfCervicalOrVaginalSmearOrScrapingCytoStain: Coding = {
  code: '47528-5',
  display: 'Cytology report of Cervical or vaginal smear or scraping Cyto stain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfTissueOtherStain: Coding = {
  code: '47529-3',
  display: 'Cytology report of Tissue Other stain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfBreastDuctalLavageCytoStain: Coding = {
  code: '47530-1',
  display: 'Cytology report of Breast ductal lavage Cyto stain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidBilateralXRay1Or2Views: Coding = {
  code: '47983-2',
  display: 'Mastoid - bilateral X-ray 1 or 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisAndSpineLumbarXRay: Coding = {
  code: '47984-0',
  display: 'Pelvis and Spine Lumbar X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCTWContrastIT: Coding = {
  code: '47985-7',
  display: 'Spine CT W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityArteriesLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '47986-5',
  display: 'Lower extremity arteries - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityArteriesRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '47987-3',
  display: 'Lower extremity arteries - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusBilateralXRay2Views: Coding = {
  code: '48433-7',
  display: 'Calcaneus - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfKidney: Coding = {
  code: '48434-5',
  display: 'US Guidance for aspiration of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSalivaryGlandBilateral: Coding = {
  code: '48435-2',
  display: 'Fluoroscopy Guidance for injection of Salivary gland - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarMRIWContrastIT: Coding = {
  code: '48436-0',
  display: 'Spine Lumbar MRI W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicMRIWContrastIT: Coding = {
  code: '48439-4',
  display: 'Spine Thoracic MRI W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullBaseMRIWContrastIV: Coding = {
  code: '48440-2',
  display: 'Skull.base MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicMRIWAndWOContrastIT: Coding = {
  code: '48441-0',
  display: 'Spine Thoracic MRI W and WO contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCTWAndWOContrastIT: Coding = {
  code: '48442-8',
  display: 'Spine CT W and WO contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasopharynxCTWAndWOContrastIV: Coding = {
  code: '48443-6',
  display: 'Nasopharynx CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainTemporalMRIWContrastIV: Coding = {
  code: '48444-4',
  display: 'Brain.temporal MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LarynxMRIWOContrast: Coding = {
  code: '48445-1',
  display: 'Larynx MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasopharynxCTWContrastIV: Coding = {
  code: '48446-9',
  display: 'Nasopharynx CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalMRIWContrastIT: Coding = {
  code: '48447-7',
  display: 'Spine Cervical MRI W contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityArteryUS: Coding = {
  code: '48448-5',
  display: 'Upper extremity artery US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitCTWContrastIV: Coding = {
  code: '48449-3',
  display: 'Orbit CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalMRIWAndWOContrastIT: Coding = {
  code: '48450-1',
  display: 'Spine Cervical MRI W and WO contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitCTWAndWOContrastIV: Coding = {
  code: '48451-9',
  display: 'Orbit CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarMRIWAndWOContrastIT: Coding = {
  code: '48452-7',
  display: 'Spine Lumbar MRI W and WO contrast IT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainTemporalMRIWOContrast: Coding = {
  code: '48453-5',
  display: 'Brain.temporal MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleRightMRIWAndWOContrastIV: Coding = {
  code: '48454-3',
  display: 'Clavicle - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleLeftMRIWAndWOContrastIV: Coding = {
  code: '48455-0',
  display: 'Clavicle - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleRightMRIWContrastIV: Coding = {
  code: '48456-8',
  display: 'Clavicle - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleLeftMRIWContrastIV: Coding = {
  code: '48457-6',
  display: 'Clavicle - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleRightMRIWOContrast: Coding = {
  code: '48458-4',
  display: 'Clavicle - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleLeftMRIWOContrast: Coding = {
  code: '48459-2',
  display: 'Clavicle - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionMRILimited: Coding = {
  code: '48460-0',
  display: 'Unspecified body region MRI limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeckMRILimited: Coding = {
  code: '48461-8',
  display: 'Neck MRI limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayAPSingleView: Coding = {
  code: '48462-6',
  display: 'Knee - left X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRayAPSingleView: Coding = {
  code: '48463-4',
  display: 'Knee - right X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TracheaFluoroscopy: Coding = {
  code: '48464-2',
  display: 'Trachea Fluoroscopy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LarynxFluoroscopy: Coding = {
  code: '48465-9',
  display: 'Larynx Fluoroscopy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayLimited: Coding = {
  code: '48466-7',
  display: 'Skull X-ray limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointXRay1Or2Views: Coding = {
  code: '48467-5',
  display: 'Sacroiliac Joint X-ray 1 or 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsBilateralAndChestXRay2ViewsAndPAChest: Coding = {
  code: '48468-3',
  display: 'Ribs - bilateral and Chest X-ray 2 views and PA chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarXRay2Or3Views: Coding = {
  code: '48469-1',
  display: 'Spine Lumbar X-ray 2 or 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidLeftXRay3Views: Coding = {
  code: '48470-9',
  display: 'Mastoid - left X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidRightXRay3Views: Coding = {
  code: '48471-7',
  display: 'Mastoid - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRay3ViewsAndSwimmers: Coding = {
  code: '48472-5',
  display: 'Spine Thoracic X-ray 3 views and Swimmers',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarAndSacrumXRay4Views: Coding = {
  code: '48473-3',
  display: 'Spine Lumbar and Sacrum X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralXRayAPAndLateral: Coding = {
  code: '48474-1',
  display: 'Hand - bilateral X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantBilateralMammogramDiagnostic: Coding = {
  code: '48475-8',
  display: 'Breast implant - bilateral Mammogram diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRayGE3Views: Coding = {
  code: '48476-6',
  display: 'Foot - right X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRayGE3Views: Coding = {
  code: '48477-4',
  display: 'Foot - left X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootBilateralXRayGE3Views: Coding = {
  code: '48478-2',
  display: 'Foot - bilateral X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesXRayGE3Views: Coding = {
  code: '48479-0',
  display: 'Facial bones X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRayGE3Views: Coding = {
  code: '48480-8',
  display: 'Ankle - bilateral X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralXRayGE3Views: Coding = {
  code: '48481-6',
  display: 'Elbow - bilateral X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SternoclavicularJointsXRayGE3Views: Coding = {
  code: '48482-4',
  display: 'Sternoclavicular Joints X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRayGE3Views: Coding = {
  code: '48483-2',
  display: 'Wrist - bilateral X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsRightAndChestXRayGE3AndPAChestViews: Coding = {
  code: '48484-0',
  display: 'Ribs - right and Chest X-ray GE 3 and PA Chest views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsBilateralAndChestXRayGE3AndPAChestViews: Coding = {
  code: '48485-7',
  display: 'Ribs - bilateral and Chest X-ray GE 3 and PA Chest views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsLeftAndChestXRayGE3AndPAChestViews: Coding = {
  code: '48486-5',
  display: 'Ribs - left and Chest X-ray GE 3 and PA Chest views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayGE4Views: Coding = {
  code: '48487-3',
  display: 'Skull X-ray GE 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidRightXRay1Or2Views: Coding = {
  code: '48488-1',
  display: 'Mastoid - right X-ray 1 or 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MastoidLeftXRay1Or2Views: Coding = {
  code: '48489-9',
  display: 'Mastoid - left X-ray 1 or 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointRightXRayOpenAndClosedMouth: Coding = {
  code: '48490-7',
  display: 'Temporomandibular joint - right X-ray open and closed mouth',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointLeftXRayOpenAndClosedMouth: Coding = {
  code: '48491-5',
  display: 'Temporomandibular joint - left X-ray open and closed mouth',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantBilateralMammogramScreening: Coding = {
  code: '48492-3',
  display: 'Breast implant - bilateral Mammogram screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullBaseMRIWOContrast: Coding = {
  code: '48687-8',
  display: 'Skull.base MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinRightUS: Coding = {
  code: '48688-6',
  display: 'Upper extremity vein - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinLeftUS: Coding = {
  code: '48689-4',
  display: 'Upper extremity vein - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinBilateralUS: Coding = {
  code: '48690-2',
  display: 'Upper extremity vein - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinRightUS: Coding = {
  code: '48691-0',
  display: 'Lower extremity vein - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinLeftUS: Coding = {
  code: '48692-8',
  display: 'Lower extremity vein - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityArteryUS: Coding = {
  code: '48693-6',
  display: 'Lower extremity artery US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainTemporalMRIWAndWOContrastIV: Coding = {
  code: '48694-4',
  display: 'Brain.temporal MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullBaseXRaySingleView: Coding = {
  code: '48695-1',
  display: 'Skull.base X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SubmandibularGlandRightFluoroscopyWContrastIntraSalivaryDuct: Coding = {
  code: '48696-9',
  display: 'Submandibular gland - right Fluoroscopy W contrast intra salivary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullBaseXRay: Coding = {
  code: '48697-7',
  display: 'Skull.base X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SubmandibularGlandBilateralFluoroscopyWContrastIntraSalivaryDuct: Coding = {
  code: '48698-5',
  display: 'Submandibular gland - bilateral Fluoroscopy W contrast intra salivary duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TemporomandibularJointUnilateralXRayOpenAndClosedMouth: Coding = {
  code: '48699-3',
  display: 'Temporomandibular Joint - unilateral X-ray open and closed mouth',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForLocalizationOfBreast: Coding = {
  code: '48735-5',
  display: 'Mammogram Guidance for localization of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForSentinelLymphNodeInjectionOfBreastLeft: Coding = {
  code: '48736-3',
  display: 'Mammogram Guidance for sentinel lymph node injection of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristAndHandXRay3Views: Coding = {
  code: '48737-1',
  display: 'Wrist and Hand X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralAndHandBilateralXRay3Views: Coding = {
  code: '48738-9',
  display: 'Wrist - bilateral and Hand - bilateral X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForSentinelLymphNodeInjectionOfBreastRight: Coding = {
  code: '48739-7',
  display: 'Mammogram Guidance for sentinel lymph node injection of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForSentinelLymphNodeInjectionOfBreast: Coding = {
  code: '48740-5',
  display: 'Mammogram Guidance for sentinel lymph node injection of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleUSDoppler: Coding = {
  code: '48742-1',
  display: 'Scrotum and Testicle US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenRetroperitoneumCTWAndWOContrastIV: Coding = {
  code: '48743-9',
  display: 'Abdomen retroperitoneum CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MandibleLeftXRay: Coding = {
  code: '48745-4',
  display: 'Mandible - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacroiliacJointBilateralXRayGE3Views: Coding = {
  code: '48746-2',
  display: 'Sacroiliac joint - bilateral X-ray GE 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrbitBilateralXRayGE4Views: Coding = {
  code: '48747-0',
  display: 'Orbit - bilateral X-ray GE 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineXRayOblique: Coding = {
  code: '48748-8',
  display: 'Spine X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayOblique: Coding = {
  code: '48749-6',
  display: 'Spine Thoracic X-ray oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SummaryPurposeCCDDocument: Coding = {
  code: '48764-5',
  display: 'Summary purpose CCD Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AllergiesAndAdverseReactionsDocument: Coding = {
  code: '48765-2',
  display: 'Allergies and adverse reactions Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InformationSource: Coding = {
  code: '48766-0',
  display: 'Information source',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnnotationCommentNarrative: Coding = {
  code: '48767-8',
  display: 'Annotation comment Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PaymentSourcesDocument: Coding = {
  code: '48768-6',
  display: 'Payment sources Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertStructuredProductLabellingIndexingDataElementsSection: Coding = {
  code: '48779-3',
  display: 'FDA package insert Structured product labelling indexing data elements section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertStructuredProductLabellingListingDataElementsSection: Coding = {
  code: '48780-1',
  display: 'FDA package insert Structured product labelling listing data elements section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BoneMarrowAspirationReport: Coding = {
  code: '48807-2',
  display: 'Bone marrow aspiration report',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionScan: Coding = {
  code: '49118-3',
  display: 'Unspecified body region Scan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertMicrobiologySection: Coding = {
  code: '49489-8',
  display: 'FDA package insert Microbiology section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionMRIWContrastIV: Coding = {
  code: '49507-7',
  display: 'Unspecified body region MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastDuctRightMammogramSingleViewWContrastIntraDuct: Coding = {
  code: '49509-3',
  display: 'Breast duct - right Mammogram Single view W contrast intra duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastDuctLeftMammogramSingleViewWContrastIntraDuct: Coding = {
  code: '49510-1',
  display: 'Breast duct - left Mammogram Single view W contrast intra duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralArteryFluoroscopicAngiogramRunoffWAndWOContrastIA: Coding = {
  code: '49511-9',
  display: 'Femoral artery Fluoroscopic angiogram runoff W and WO contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy: Coding = {
  code: '49512-7',
  display: 'Unspecified body region Fluoroscopy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSpineVesselsMRIAngiogram: Coding = {
  code: '49565-5',
  display: 'Thoracic Spine vessels MRI angiogram',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTAtRestAndWTc99mSestamibiIV: Coding = {
  code: '49566-3',
  display: 'Heart SPECT at rest and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionWAdenosineAndWTc99mSestamibiIV: Coding = {
  code: '49567-1',
  display: 'Heart SPECT perfusion W adenosine and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionAtRestAndWStressAndWTl201IVAndWTc99mSestamibiIV: Coding = {
  code: '49568-9',
  display: 'Heart SPECT perfusion at rest and W stress and W Tl-201 IV and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartSPECTPerfusionAndWallMotionAtRestAndWStressAndWTl201IVAndWTc99mSestamibiIV: Coding = {
  code: '49569-7',
  display: 'Heart SPECT perfusion and wall motion at rest and W stress and W Tl-201 IV and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralXRayGE6Views: Coding = {
  code: '49570-5',
  display: 'Ankle - bilateral X-ray GE 6 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScanLimitedWI131MIBGIV: Coding = {
  code: '49571-3',
  display: 'Scan limited W I-131 MIBG IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfBronchoalveolarLavageCytoStain: Coding = {
  code: '50007-4',
  display: 'Cytology report of Bronchoalveolar lavage Cyto stain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL18HydroxydeoxycorticosteroneMolesVolumeInSerumOrPlasma: Coding = {
  code: '50081-9',
  display: '18-Hydroxydeoxycorticosterone [Moles/volume] in Serum or Plasma',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityBilateralCTWContrastIV: Coding = {
  code: '50755-8',
  display: 'Lower extremity - bilateral CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytologyReportOfBronchialBrushCytoStain: Coding = {
  code: '50971-1',
  display: 'Cytology report of Bronchial brush Cyto stain',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRayAndAPViewStanding: Coding = {
  code: '51387-9',
  display: 'Knee - bilateral X-ray and (AP view standing)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightAndHandRightXRay: Coding = {
  code: '51388-7',
  display: 'Wrist - right and Hand - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastScanWTl201IV: Coding = {
  code: '51389-5',
  display: 'Breast Scan W Tl-201 IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfTransjugularIntrahepaticPortosystemicShuntInPortalVeinAndHepaticVein: Coding = {
  code: '51391-1',
  display: 'Fluoroscopic angiogram Guidance for placement of transjugular intrahepatic portosystemic shunt in Portal vein and Hepatic vein',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftAndHandLeftXRay: Coding = {
  code: '51392-9',
  display: 'Wrist - left and Hand - left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightAndFootRightXRay: Coding = {
  code: '51394-5',
  display: 'Ankle - right and Foot - right X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftAndFootLeftXRay: Coding = {
  code: '51395-2',
  display: 'Ankle - left and Foot.left X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OutpatientConsultNote: Coding = {
  code: '51845-6',
  display: 'Outpatient Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EmergencyDepartmentConsultNote: Coding = {
  code: '51846-4',
  display: 'Emergency department Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AssessmentPlusPlanNote: Coding = {
  code: '51847-2',
  display: 'Assessment + Plan note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AssessmentNote: Coding = {
  code: '51848-0',
  display: 'Assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdmissionHistoryAndPhysicalNote: Coding = {
  code: '51849-8',
  display: 'Admission history and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicalFindingsOfHeadAndEarsAndEyesAndNoseAndThroat: Coding = {
  code: '51850-6',
  display: 'Physical findings of Head and Ears and Eyes and Nose and Throat',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdministrativeNote: Coding = {
  code: '51851-4',
  display: 'Administrative note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_Letter: Coding = {
  code: '51852-2',
  display: 'Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LongTermCareFacilityConsultNote: Coding = {
  code: '51854-8',
  display: 'Long term care facility Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatientNote: Coding = {
  code: '51855-5',
  display: 'Patient Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HealthcareAssociatedInfectionReportDocument: Coding = {
  code: '51897-7',
  display: 'Healthcare Associated Infection report Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RiskFactorsDocument: Coding = {
  code: '51898-5',
  display: 'Risk factors Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DetailsDocument: Coding = {
  code: '51899-3',
  display: 'Details Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PopulationSummaryNote: Coding = {
  code: '51900-9',
  display: 'Population Summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelBackPanelOfPackage: Coding = {
  code: '51941-3',
  display: 'FDA product label Back panel of package',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelSidePanelOfPackageRight: Coding = {
  code: '51942-1',
  display: 'FDA product label Side panel of package Right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelSidePanelOfPackageLeft: Coding = {
  code: '51943-9',
  display: 'FDA product label Side panel of package Left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelSidePanelOfPackage: Coding = {
  code: '51944-7',
  display: 'FDA product label Side panel of package',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelPrincipalDisplayPanelOfPackage: Coding = {
  code: '51945-4',
  display: 'FDA product label Principal display panel of package',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelTopPanelOfPackage: Coding = {
  code: '51946-2',
  display: 'FDA product label Top panel of package',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelBottomPanelOfPackage: Coding = {
  code: '51947-0',
  display: 'FDA product label Bottom panel of package',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelFlapPanelOfPackage: Coding = {
  code: '51948-8',
  display: 'FDA product label Flap panel of package',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PharmacogeneticAnalysisReportInBloodOrTissueDocumentByMolecularGeneticsMethod: Coding = {
  code: '51965-2',
  display: 'Pharmacogenetic analysis report in Blood or Tissue Document by Molecular genetics method',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneticAnalysisSummaryReportInBloodOrTissueDocumentByMolecularGeneticsMethod: Coding = {
  code: '51969-4',
  display: 'Genetic analysis summary report in Blood or Tissue Document by Molecular genetics method',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbortionConsent: Coding = {
  code: '52027-0',
  display: 'Abortion consent',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HysterectomyConsent: Coding = {
  code: '52028-8',
  display: 'Hysterectomy consent',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SterilizationConsent: Coding = {
  code: '52029-6',
  display: 'Sterilization consent',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExplanationOfBenefits: Coding = {
  code: '52030-4',
  display: 'Explanation of benefits',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExplanationOfBenefitsToSubscriber: Coding = {
  code: '52031-2',
  display: 'Explanation of benefits to subscriber',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AppealDenialLetter: Coding = {
  code: '52032-0',
  display: 'Appeal denial letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralCorrespondence: Coding = {
  code: '52033-8',
  display: 'General correspondence',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PayerLetter: Coding = {
  code: '52034-6',
  display: 'Payer letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HomeHealthClaims: Coding = {
  code: '52035-3',
  display: 'Home health claims',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HomeHealthPriorAuthorization: Coding = {
  code: '52036-1',
  display: 'Home health prior authorization',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MemberIDCardCopy: Coding = {
  code: '52037-9',
  display: 'Member ID card copy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SubscriberInformationIncludingRetroactiveAndPresumptiveEligibility: Coding = {
  code: '52038-7',
  display: 'Subscriber Information including retroactive and presumptive eligibility',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkilledNursingFacilitySNFRecord: Coding = {
  code: '52039-5',
  display: 'Skilled Nursing Facility (SNF) record',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DentalXRaysAndOtherImagesNotDICOM: Coding = {
  code: '52040-3',
  display: 'Dental X-rays and other images (not DICOM)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BloodGlucoseMonitors: Coding = {
  code: '52041-1',
  display: 'Blood glucose monitors',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ContinuousPositiveAirwayPressureCPAP: Coding = {
  code: '52042-9',
  display: 'Continuous positive airway pressure (CPAP)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EnteralNutrition: Coding = {
  code: '52043-7',
  display: 'Enteral nutrition',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExternalInfusionPump: Coding = {
  code: '52044-5',
  display: 'External infusion pump',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GaitTrainers: Coding = {
  code: '52045-2',
  display: 'Gait trainers',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalBeds: Coding = {
  code: '52046-0',
  display: 'Hospital beds',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ImmunosuppressiveDrugs: Coding = {
  code: '52047-8',
  display: 'Immunosuppressive drugs',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LymphedemaPumps: Coding = {
  code: '52048-6',
  display: 'Lymphedema pumps',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ManualWheelchair: Coding = {
  code: '52049-4',
  display: 'Manual wheelchair',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MotorizedWheelchair: Coding = {
  code: '52050-2',
  display: 'Motorized wheelchair',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrthoticsProsthetics: Coding = {
  code: '52051-0',
  display: 'Orthotics/Prosthetics',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OsteogenesisStimulators: Coding = {
  code: '52052-8',
  display: 'Osteogenesis stimulators',
  system: 'http://loinc.org',
};
const C80DocTypecodes_Oxygen: Coding = {
  code: '52053-6',
  display: 'Oxygen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_Parenteral: Coding = {
  code: '52054-4',
  display: 'Parenteral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PowerOperatedVehicles: Coding = {
  code: '52055-1',
  display: 'Power operated vehicles',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RepairOfDurableMedicalEquipment: Coding = {
  code: '52056-9',
  display: 'Repair of durable medical equipment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SeatLiftMechanism: Coding = {
  code: '52057-7',
  display: 'Seat lift mechanism',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SeatingSystems: Coding = {
  code: '52058-5',
  display: 'Seating systems',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechGeneratingDevice: Coding = {
  code: '52059-3',
  display: 'Speech generating device',
  system: 'http://loinc.org',
};
const C80DocTypecodes_StandersStandingFrames: Coding = {
  code: '52060-1',
  display: 'Standers/standing frames',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SupportSurfaces: Coding = {
  code: '52061-9',
  display: 'Support surfaces',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TranscutaneousElectricalNeuralStimulationTENS: Coding = {
  code: '52062-7',
  display: 'Transcutaneous electrical neural stimulation (TENS)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrescriptionForDurableMedicalEquipmentDME: Coding = {
  code: '52063-5',
  display: 'Prescription for durable medical equipment (DME)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FirstReportOfInjury: Coding = {
  code: '52064-3',
  display: 'First report of injury',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AutomobileLiability: Coding = {
  code: '52065-0',
  display: 'Automobile liability',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NoticeOfDischargeMedicareAppealRightsNODMARForm: Coding = {
  code: '52066-8',
  display: 'Notice of Discharge Medicare Appeal Rights (NODMAR) form',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PastFilingLimitJustification: Coding = {
  code: '52067-6',
  display: 'Past filing limit justification',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PropertyAndCasualtyStateMandatedForms: Coding = {
  code: '52068-4',
  display: 'Property and casualty state mandated forms',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TaxIDNumberIRSFormW9: Coding = {
  code: '52069-2',
  display: 'Tax ID number - IRS form W9',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WorkersCompensation: Coding = {
  code: '52070-0',
  display: 'Workers compensation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EmployeeAssistanceProgram: Coding = {
  code: '52071-8',
  display: 'Employee assistance program',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NonEmergencyTransportation: Coding = {
  code: '52072-6',
  display: 'Non-emergency transportation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VisionAttachment: Coding = {
  code: '52073-4',
  display: 'Vision attachment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PurchaseInvoice: Coding = {
  code: '52075-9',
  display: 'Purchase invoice',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryTherapyServiceAttachment: Coding = {
  code: '52184-9',
  display: 'Pulmonary therapy service attachment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForReplacementOfPercutaneousDrainageTubeInAbdomen: Coding = {
  code: '52790-3',
  display: 'CT Guidance for replacement of percutaneous drainage tube in Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForReplacementOfPercutaneousDrainageTubeInPelvis: Coding = {
  code: '52791-1',
  display: 'CT Guidance for replacement of percutaneous drainage tube in Pelvis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChargeTicketOrEncounterForm: Coding = {
  code: '53242-4',
  display: 'Charge ticket or encounter form',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdvancedBeneficiaryNotice: Coding = {
  code: '53243-2',
  display: 'Advanced beneficiary notice',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NoticeOfPrivacyPracticesReceipt: Coding = {
  code: '53244-0',
  display: 'Notice of privacy practices receipt',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DriverLicenseImage: Coding = {
  code: '53245-7',
  display: 'Driver license image',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NonMedicalServices: Coding = {
  code: '53246-5',
  display: 'Non-medical services',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EligibilityAcknowledgement: Coding = {
  code: '53247-3',
  display: 'Eligibility acknowledgement',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInDriedBloodSpot: Coding = {
  code: '53347-1',
  display: '11-Deoxycorticosterone [Mass/volume] in Dried blood spot',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMolesVolumeInDriedBloodSpot: Coding = {
  code: '53348-9',
  display: '11-Deoxycorticosterone [Moles/volume] in Dried blood spot',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PersonalHealthMonitoringReportDocument: Coding = {
  code: '53576-5',
  display: 'Personal health monitoring report Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EmergencyDepartmentTriageNote: Coding = {
  code: '54094-8',
  display: 'Emergency department Triage note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChemotherapyEffectivenessPanelIdentifierBloodOrTissue: Coding = {
  code: '54095-5',
  display: 'Chemotherapy effectiveness panel [Identifier] - Blood or Tissue',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertUserSafetyWarningsSection: Coding = {
  code: '54433-8',
  display: 'FDA package insert User safety warnings section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AddendumDocument: Coding = {
  code: '55107-7',
  display: 'Addendum Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalPresentationDocument: Coding = {
  code: '55108-5',
  display: 'Clinical presentation Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ComplicationsDocument: Coding = {
  code: '55109-3',
  display: 'Complications Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ConclusionsDocument: Coding = {
  code: '55110-1',
  display: 'Conclusions Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CurrentImagingProcedureDescriptionsDocument: Coding = {
  code: '55111-9',
  display: 'Current imaging procedure descriptions Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DocumentSummary: Coding = {
  code: '55112-7',
  display: 'Document summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KeyImagesDocumentRadiology: Coding = {
  code: '55113-5',
  display: 'Key images Document Radiology',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PriorImagingProcedureDescriptionsDocument: Coding = {
  code: '55114-3',
  display: 'Prior imaging procedure descriptions Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RequestedImagingStudiesInformationDocument: Coding = {
  code: '55115-0',
  display: 'Requested imaging studies information Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgicalOperationNoteImplantsNarrative: Coding = {
  code: '55122-6',
  display: 'Surgical operation note implants Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_QualityReportingDocumentArchitectureIncidenceReportDocument: Coding = {
  code: '55182-0',
  display: 'Quality Reporting Document Architecture incidence report Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_QualityReportingDocumentArchitecturePatientListReportPopulationDocument: Coding = {
  code: '55183-8',
  display: 'Quality Reporting Document Architecture patient list report population Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_QualityReportingDocumentArchitectureCalculatedSummaryReportPopulationDocument: Coding = {
  code: '55184-6',
  display: 'Quality Reporting Document Architecture calculated summary report population Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MeasureSetDocument: Coding = {
  code: '55185-3',
  display: 'Measure set Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MeasureDocument: Coding = {
  code: '55186-1',
  display: 'Measure Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ReportingParametersDocument: Coding = {
  code: '55187-9',
  display: 'Reporting parameters Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatientDataDocument: Coding = {
  code: '55188-7',
  display: 'Patient data Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CytogeneticsStudy: Coding = {
  code: '55228-1',
  display: 'Cytogenetics study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ImmuneStainStudy: Coding = {
  code: '55229-9',
  display: 'Immune stain study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ImmunophenotypingStudy: Coding = {
  code: '55230-7',
  display: 'Immunophenotyping study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatientSafetyReportEventDocument: Coding = {
  code: '55750-4',
  display: 'Patient safety report Event Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PublicHealthCaseReportDocument: Coding = {
  code: '55751-2',
  display: 'Public health case report Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMolesTimeIn24HourUrine: Coding = {
  code: '55808-0',
  display: '11-Deoxycorticosterone [Moles/time] in 24 hour Urine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HealthcareCommunicationDocument: Coding = {
  code: '56444-3',
  display: 'Healthcare communication Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MedicationSummaryDocument: Coding = {
  code: '56445-0',
  display: 'Medication summary Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AppointmentSummaryDocument: Coding = {
  code: '56446-8',
  display: 'Appointment summary Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlanOfCareNote: Coding = {
  code: '56447-6',
  display: 'Plan of care note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasmaBaseline: Coding = {
  code: '56555-6',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --baseline',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma30MinutesPostXXXChallenge: Coding = {
  code: '56556-4',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --30 minutes post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma1stSpecimenPostXXXChallenge: Coding = {
  code: '56602-6',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --1st specimen post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma2ndSpecimenPostXXXChallenge: Coding = {
  code: '56603-4',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --2nd specimen post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma3rdSpecimenPostXXXChallenge: Coding = {
  code: '56604-2',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --3rd specimen post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma4thSpecimenPostXXXChallenge: Coding = {
  code: '56605-9',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --4th specimen post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasmaPreXXXChallenge: Coding = {
  code: '56606-7',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --pre XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma15MinutesPostXXXChallenge: Coding = {
  code: '56608-3',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --15 minutes post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma20MinutesPostXXXChallenge: Coding = {
  code: '56609-1',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --20 minutes post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma40MinutesPostXXXChallenge: Coding = {
  code: '56610-9',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --40 minutes post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma15HoursPostXXXChallenge: Coding = {
  code: '56611-7',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --1.5 hours post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma2HoursPostXXXChallenge: Coding = {
  code: '56612-5',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --2 hours post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma25HoursPostXXXChallenge: Coding = {
  code: '56613-3',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --2.5 hours post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrivacyPolicyAcknowledgmentDocument: Coding = {
  code: '57016-8',
  display: 'Privacy policy acknowledgment Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrivacyPolicyOrganizationDocument: Coding = {
  code: '57017-6',
  display: 'Privacy policy Organization Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HealthQualityMeasureDocument: Coding = {
  code: '57024-2',
  display: 'Health Quality Measure document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DataCriteriaNarrative: Coding = {
  code: '57025-9',
  display: 'Data criteria Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PopulationCriteriaNarrative: Coding = {
  code: '57026-7',
  display: 'Population criteria Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MeasureObservationsNarrative: Coding = {
  code: '57027-5',
  display: 'Measure observations Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseEmergencyDepartmentNote: Coding = {
  code: '57053-1',
  display: 'Nurse Emergency department Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseEmergencyDepartmentTriagePlusCareNote: Coding = {
  code: '57054-9',
  display: 'Nurse Emergency department Triage+care note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AntepartumSummaryNote: Coding = {
  code: '57055-6',
  display: 'Antepartum summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LaborAndDeliveryAdmissionHistoryAndPhysicalNote: Coding = {
  code: '57056-4',
  display: 'Labor and delivery admission history and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LaborAndDeliverySummaryNote: Coding = {
  code: '57057-2',
  display: 'Labor and delivery summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MaternalDischargeSummaryNote: Coding = {
  code: '57058-0',
  display: 'Maternal discharge summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PregnancyVisitSummaryNoteNarrative: Coding = {
  code: '57059-8',
  display: 'Pregnancy visit summary note Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FullNewbornScreeningSummaryReportForDisplayOrPrinting: Coding = {
  code: '57129-9',
  display: 'Full newborn screening summary report for display or printing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ReferralNote: Coding = {
  code: '57133-1',
  display: 'Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DentistryReferralNote: Coding = {
  code: '57134-9',
  display: 'Dentistry Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DermatologyReferralNote: Coding = {
  code: '57135-6',
  display: 'Dermatology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DiabetologyReferralNote: Coding = {
  code: '57136-4',
  display: 'Diabetology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EndocrinologyReferralNote: Coding = {
  code: '57137-2',
  display: 'Endocrinology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GastroenterologyReferralNote: Coding = {
  code: '57138-0',
  display: 'Gastroenterology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineReferralNote: Coding = {
  code: '57139-8',
  display: 'General medicine Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InfectiousDiseaseReferralNote: Coding = {
  code: '57141-4',
  display: 'Infectious disease Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KinesiotherapyReferralNote: Coding = {
  code: '57142-2',
  display: 'Kinesiotherapy Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MentalHealthReferralNote: Coding = {
  code: '57143-0',
  display: 'Mental health Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NephrologyReferralNote: Coding = {
  code: '57144-8',
  display: 'Nephrology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyReferralNote: Coding = {
  code: '57145-5',
  display: 'Neurology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologicalSurgeryReferralNote: Coding = {
  code: '57146-3',
  display: 'Neurological surgery Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalMedicineReferralNote: Coding = {
  code: '57147-1',
  display: 'Occupational medicine Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalTherapyReferralNote: Coding = {
  code: '57148-9',
  display: 'Occupational therapy Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OncologyReferralNote: Coding = {
  code: '57149-7',
  display: 'Oncology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OphthalmologyReferralNote: Coding = {
  code: '57150-5',
  display: 'Ophthalmology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OptometryReferralNote: Coding = {
  code: '57151-3',
  display: 'Optometry Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PharmacyReferralNote: Coding = {
  code: '57152-1',
  display: 'Pharmacy Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicalMedicineAndRehabilitationReferralNote: Coding = {
  code: '57153-9',
  display: 'Physical medicine and rehabilitation Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicalTherapyReferralNote: Coding = {
  code: '57154-7',
  display: 'Physical therapy Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlasticSurgeryReferralNote: Coding = {
  code: '57155-4',
  display: 'Plastic surgery Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PodiatryReferralNote: Coding = {
  code: '57156-2',
  display: 'Podiatry Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychiatryReferralNote: Coding = {
  code: '57157-0',
  display: 'Psychiatry Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychologyReferralNote: Coding = {
  code: '57158-8',
  display: 'Psychology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiationOncologyReferralNote: Coding = {
  code: '57159-6',
  display: 'Radiation oncology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RecreationalTherapyReferralNote: Coding = {
  code: '57160-4',
  display: 'Recreational therapy Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RespiratoryTherapyReferralNote: Coding = {
  code: '57162-0',
  display: 'Respiratory therapy Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RheumatologyReferralNote: Coding = {
  code: '57163-8',
  display: 'Rheumatology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SocialWorkReferralNote: Coding = {
  code: '57164-6',
  display: 'Social work Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechLanguagePathologyReferralNote: Coding = {
  code: '57165-3',
  display: 'Speech-language pathology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryReferralNote: Coding = {
  code: '57166-1',
  display: 'Surgery Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSurgeryReferralNote: Coding = {
  code: '57167-9',
  display: 'Thoracic surgery Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrologyReferralNote: Coding = {
  code: '57168-7',
  display: 'Urology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VascularSurgeryReferralNote: Coding = {
  code: '57169-5',
  display: 'Vascular surgery Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyReferralNote: Coding = {
  code: '57170-3',
  display: 'Cardiology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeriatricMedicineReferralNote: Coding = {
  code: '57171-1',
  display: 'Geriatric medicine Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HematologyPlusMedicalOncologyReferralNote: Coding = {
  code: '57172-9',
  display: 'Hematology+Medical Oncology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NutritionAndDieteticsReferralNote: Coding = {
  code: '57173-7',
  display: 'Nutrition and dietetics Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OralAndMaxillofacialSurgeryReferralNote: Coding = {
  code: '57174-5',
  display: 'Oral and Maxillofacial Surgery Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrthopaedicSurgeryReferralNote: Coding = {
  code: '57175-2',
  display: 'Orthopaedic surgery Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OtolaryngologyReferralNote: Coding = {
  code: '57176-0',
  display: 'Otolaryngology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryReferralNote: Coding = {
  code: '57177-8',
  display: 'Pulmonary Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CriticalCareMedicineReferralNote: Coding = {
  code: '57178-6',
  display: 'Critical Care Medicine Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ObstetricsAndGynecologyReferralNote: Coding = {
  code: '57179-4',
  display: 'Obstetrics and Gynecology Referral note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMolesVolumeInSerumOrPlasmaPre250UgCorticotropin: Coding = {
  code: '57491-3',
  display: '11-Deoxycorticosterone [Moles/volume] in Serum or Plasma --pre 250 ug corticotropin',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMolesVolumeInSerumOrPlasma30MinutesPost250UgCorticotropin: Coding = {
  code: '57492-1',
  display: '11-Deoxycorticosterone [Moles/volume] in Serum or Plasma --30 minutes post 250 ug corticotropin',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMolesVolumeInSerumOrPlasma1HourPost250UgCorticotropin: Coding = {
  code: '57493-9',
  display: '11-Deoxycorticosterone [Moles/volume] in Serum or Plasma --1 hour post 250 ug corticotropin',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL18HydroxydeoxycorticosteroneMolesVolumeInSerumOrPlasmaPreDoseCorticotropin: Coding = {
  code: '57551-4',
  display: '18-Hydroxydeoxycorticosterone [Moles/volume] in Serum or Plasma --pre dose corticotropin',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL18HydroxydeoxycorticosteroneMolesVolumeInSerumOrPlasma30MinutesPostDoseCorticotropin: Coding = {
  code: '57552-2',
  display: '18-Hydroxydeoxycorticosterone [Moles/volume] in Serum or Plasma --30 minutes post dose corticotropin',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL18HydroxydeoxycorticosteroneMolesVolumeInSerumOrPlasma1HourPostDoseCorticotropin: Coding = {
  code: '57553-0',
  display: '18-Hydroxydeoxycorticosterone [Moles/volume] in Serum or Plasma --1 hour post dose corticotropin',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL21DeoxycorticosteroneMolesVolumeInSerumOrPlasmaPreDoseCorticotropin: Coding = {
  code: '57560-5',
  display: '21-Deoxycorticosterone [Moles/volume] in Serum or Plasma --pre dose corticotropin',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL21DeoxycorticosteroneMolesVolumeInSerumOrPlasma30MinutesPostDoseCorticotropin: Coding = {
  code: '57561-3',
  display: '21-Deoxycorticosterone [Moles/volume] in Serum or Plasma --30 minutes post dose corticotropin',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL21DeoxycorticosteroneMolesVolumeInSerumOrPlasma1HourPostDoseCorticotropin: Coding = {
  code: '57562-1',
  display: '21-Deoxycorticosterone [Moles/volume] in Serum or Plasma --1 hour post dose corticotropin',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungPET: Coding = {
  code: '57822-9',
  display: 'Lung PET',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EsophagusPET: Coding = {
  code: '57823-7',
  display: 'Esophagus PET',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CoPaymentAmountNarrative: Coding = {
  code: '57826-0',
  display: 'Co-payment amount Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ReasonForCoPaymentExemptionNarrative: Coding = {
  code: '57827-8',
  display: 'Reason for co-payment exemption Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrescriptionListDocument: Coding = {
  code: '57828-6',
  display: 'Prescription list Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrescriptionForMedicalEquipmentOrProductDocument: Coding = {
  code: '57829-4',
  display: 'Prescription for medical equipment or product Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdmissionRequestDocument: Coding = {
  code: '57830-2',
  display: 'Admission request Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrescriptionForRehabilitationDocument: Coding = {
  code: '57831-0',
  display: 'Prescription for rehabilitation Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrescriptionForDiagnosticOrSpecialistCareDocument: Coding = {
  code: '57832-8',
  display: 'Prescription for diagnostic or specialist care Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrescriptionForMedicationDocument: Coding = {
  code: '57833-6',
  display: 'Prescription for medication Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatientTransportationRequestDocument: Coding = {
  code: '57834-4',
  display: 'Patient transportation request Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryFunctionReport: Coding = {
  code: '58477-1',
  display: 'Pulmonary function report',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenMRCPWOContrast: Coding = {
  code: '58740-2',
  display: 'Abdomen MRCP WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadToThighPET: Coding = {
  code: '58741-0',
  display: 'Head to thigh PET',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadAndNeckPET: Coding = {
  code: '58742-8',
  display: 'Head and Neck PET',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAblationOfTissueOfUnspecifiedBodyRegion: Coding = {
  code: '58743-6',
  display: 'US Guidance for ablation of tissue of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartCT: Coding = {
  code: '58744-4',
  display: 'Heart CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CoronaryArteriesCTAngiogramAnd3DReconstructionWContrastIV: Coding = {
  code: '58745-1',
  display: 'Coronary arteries CT angiogram and 3D reconstruction W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AVFistulaFluoroscopicAngiogramWContrastIV: Coding = {
  code: '58746-9',
  display: 'AV fistula Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForAblationOfTissueOfUnspecifiedBodyRegion: Coding = {
  code: '58747-7',
  display: 'CT Guidance for ablation of tissue of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrainFunctionalMRI: Coding = {
  code: '58748-5',
  display: 'Brain Functional MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartMRIWStressAndWAndWOContrastIV: Coding = {
  code: '58749-3',
  display: 'Heart MRI W stress and W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartMRIWStress: Coding = {
  code: '58750-1',
  display: 'Heart MRI W stress',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LeftAtriumAndPulmonaryVeinsCTAngiogramAnd3DReconstructionWContrastIV: Coding = {
  code: '59255-0',
  display: 'Left atrium and Pulmonary veins CT angiogram and 3D reconstruction W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EmergencyDepartmentDischargeSummary: Coding = {
  code: '59258-4',
  display: 'Emergency department Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychiatryDischargeSummary: Coding = {
  code: '59259-2',
  display: 'Psychiatry Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeonatalCareReport: Coding = {
  code: '59268-3',
  display: 'Neonatal care report',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TransthoracicCardiacEchoStudyReportUS: Coding = {
  code: '59281-6',
  display: 'Transthoracic cardiac echo study report US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_StressCardiacEchoStudyReportUS: Coding = {
  code: '59282-4',
  display: 'Stress cardiac echo study report US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WellChildVisitNote: Coding = {
  code: '59283-2',
  display: 'Well child visit note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatientConsent: Coding = {
  code: '59284-0',
  display: 'Patient Consent',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProcedureIndicationsNarrative: Coding = {
  code: '59768-2',
  display: 'Procedure indications Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PostprocedureDiagnosisNarrative: Coding = {
  code: '59769-0',
  display: 'Postprocedure diagnosis Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProcedureEstimatedBloodLossNarrative: Coding = {
  code: '59770-8',
  display: 'Procedure estimated blood loss Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProcedureImplantsNarrative: Coding = {
  code: '59771-6',
  display: 'Procedure implants Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlannedProcedureNarrative: Coding = {
  code: '59772-4',
  display: 'Planned procedure Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProcedureSpecimensTakenNarrative: Coding = {
  code: '59773-2',
  display: 'Procedure specimens taken Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProcedureAnesthesiaNarrative: Coding = {
  code: '59774-0',
  display: 'Procedure anesthesia Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProcedureDispositionNarrative: Coding = {
  code: '59775-7',
  display: 'Procedure disposition Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProcedureFindingsNarrative: Coding = {
  code: '59776-5',
  display: 'Procedure findings Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertInstructionsForUseSection: Coding = {
  code: '59845-8',
  display: 'FDA package insert Instructions for use section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma8thSpecimenPostXXXChallenge: Coding = {
  code: '59984-5',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --8th specimen post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma7thSpecimenPostXXXChallenge: Coding = {
  code: '59985-2',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --7th specimen post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma6thSpecimenPostXXXChallenge: Coding = {
  code: '59986-0',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --6th specimen post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma5thSpecimenPostXXXChallenge: Coding = {
  code: '59987-8',
  display: '11-Deoxycorticosterone [Mass/volume] in Serum or Plasma --5th specimen post XXX challenge',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EmergencyDepartmentDischargeInstructions: Coding = {
  code: '60280-5',
  display: 'Emergency department Discharge instructions',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RectumAndColonCT3DWAirContrastPR: Coding = {
  code: '60515-4',
  display: 'Rectum and Colon CT 3D W air contrast PR',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanAndUptakeWI123PO: Coding = {
  code: '60527-9',
  display: 'Thyroid Scan and uptake W I-123 PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertAccessories: Coding = {
  code: '60555-0',
  display: 'FDA package insert Accessories',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertAssemblyOrInstallationInstructions: Coding = {
  code: '60556-8',
  display: 'FDA package insert Assembly or installation instructions',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertCalibrationInstructions: Coding = {
  code: '60557-6',
  display: 'FDA package insert Calibration instructions',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertCleaningDisinfectingAndSterilizationInstructions: Coding = {
  code: '60558-4',
  display: 'FDA package insert Cleaning, disinfecting, and sterilization instructions',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertComponents: Coding = {
  code: '60559-2',
  display: 'FDA package insert Components',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertIntendedUseOfTheDevice: Coding = {
  code: '60560-0',
  display: 'FDA package insert Intended use of the device',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertOtherSafetyInformation: Coding = {
  code: '60561-8',
  display: 'FDA package insert Other safety information',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SynopticReport: Coding = {
  code: '60568-3',
  display: 'Synoptic report',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ReportAddendumSynopticDocument: Coding = {
  code: '60569-1',
  display: 'Report addendum.synoptic Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PathologyConsultNote: Coding = {
  code: '60570-9',
  display: 'Pathology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PathologyConsultNoteSynoptic: Coding = {
  code: '60571-7',
  display: 'Pathology Consult note.synoptic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ReportTemplateID: Coding = {
  code: '60572-5',
  display: 'Report template ID',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ReportTemplateSource: Coding = {
  code: '60573-3',
  display: 'Report template source',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ReportTemplateVersionID: Coding = {
  code: '60574-1',
  display: 'Report template version ID',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MedicationDispensedBriefDocument: Coding = {
  code: '60590-7',
  display: 'Medication dispensed.brief Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatientSummaryDocument: Coding = {
  code: '60591-5',
  display: 'Patient summary Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatientSummaryUnexpectedContactDocument: Coding = {
  code: '60592-3',
  display: 'Patient summary.unexpected contact Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MedicationDispensedExtendedDocument: Coding = {
  code: '60593-1',
  display: 'Medication dispensed.extended Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelPlasmaDerivative: Coding = {
  code: '60683-0',
  display: 'FDA product label Plasma derivative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelCellularTherapy: Coding = {
  code: '60684-8',
  display: 'FDA product label Cellular therapy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertIndexingPharmacologicClass: Coding = {
  code: '60685-5',
  display: 'FDA package insert Indexing - pharmacologic class',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseSummaryNote: Coding = {
  code: '61143-4',
  display: 'Nurse Summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GoalsNarrative: Coding = {
  code: '61146-7',
  display: 'Goals Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExpectedOutcomesNarrative: Coding = {
  code: '61147-5',
  display: 'Expected outcomes Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ObjectiveNarrative: Coding = {
  code: '61149-1',
  display: 'Objective Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SubjectiveNarrative: Coding = {
  code: '61150-9',
  display: 'Subjective Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MedicationPharmaceuticalAdviceExtendedDocument: Coding = {
  code: '61356-2',
  display: 'Medication pharmaceutical advice.extended Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MedicationPharmaceuticalAdviceBriefDocument: Coding = {
  code: '61357-0',
  display: 'Medication pharmaceutical advice.brief Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatientSurgicalOperationConsent: Coding = {
  code: '61358-8',
  display: 'Patient Surgical operation consent',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatientAnesthesiaConsent: Coding = {
  code: '61359-6',
  display: 'Patient Anesthesia consent',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RecommendationInterpretationDocument: Coding = {
  code: '62385-0',
  display: 'Recommendation [interpretation] Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterventionsNarrative: Coding = {
  code: '62387-6',
  display: 'Interventions Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '62446-0',
  display: 'Renal artery - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '62447-8',
  display: 'Renal artery - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadArteryLeftPlusNeckArteryLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '62448-6',
  display: 'Head artery.left+Neck artery.left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadArteryRightPlusNeckArteryRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '62449-4',
  display: 'Head artery.right+Neck artery.right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfIntraperitonealCatheterInAbdomen: Coding = {
  code: '62450-2',
  display: 'Fluoroscopic angiogram Guidance for placement of intraperitoneal catheter in Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityLeftUSLimited: Coding = {
  code: '62451-0',
  display: 'Extremity - left US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityRightUSLimited: Coding = {
  code: '62452-8',
  display: 'Extremity - right US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfIlioIliacTubeEndoprosthesisInIliacArteryLeftWContrastIA: Coding = {
  code: '62491-6',
  display: 'Fluoroscopic angiogram Guidance for placement of ilio-iliac tube endoprosthesis in Iliac artery - left-- W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfIlioIliacTubeEndoprosthesisInIliacArteryRightWContrastIA: Coding = {
  code: '62492-4',
  display: 'Fluoroscopic angiogram Guidance for placement of ilio-iliac tube endoprosthesis in Iliac artery - right-- W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForPercutaneousDrainageOfCavity: Coding = {
  code: '62494-0',
  display: 'US Guidance for percutaneous drainage of Cavity',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ComputerGeneratedRecommendationDocument: Coding = {
  code: '63485-7',
  display: 'Computer generated recommendation Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLymphaticsLeftScanWRadionuclideIntraLymphatic: Coding = {
  code: '64051-6',
  display: 'Breast lymphatics - left Scan W radionuclide intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLymphaticsRightScanWRadionuclideIntraLymphatic: Coding = {
  code: '64052-4',
  display: 'Breast lymphatics - right Scan W radionuclide intra lymphatic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineHospitalAdmissionEvaluationNote: Coding = {
  code: '64053-2',
  display: 'General medicine Hospital Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineMedicalStudentHospitalAdmissionEvaluationNote: Coding = {
  code: '64054-0',
  display: 'General medicine Medical student Hospital Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineMedicalStudentHospitalProgressNote: Coding = {
  code: '64055-7',
  display: 'General medicine Medical student Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicineMedicalStudentHospitalConsultNote: Coding = {
  code: '64056-5',
  display: 'General medicine Medical student Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryHospitalProgressNote: Coding = {
  code: '64057-3',
  display: 'Surgery Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CriticalCareMedicineHospitalAdmissionEvaluationNote: Coding = {
  code: '64058-1',
  display: 'Critical Care Medicine Hospital Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CriticalCareMedicineHospitalProgressNote: Coding = {
  code: '64059-9',
  display: 'Critical Care Medicine Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSurgeryHospitalAdmissionEvaluationNote: Coding = {
  code: '64060-7',
  display: 'Thoracic surgery Hospital Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSurgeryHospitalProgressNote: Coding = {
  code: '64061-5',
  display: 'Thoracic surgery Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryHospitalAdmissionEvaluationNote: Coding = {
  code: '64062-3',
  display: 'Pulmonary Hospital Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryHospitalProgressNote: Coding = {
  code: '64063-1',
  display: 'Pulmonary Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PastoralCareHospitalAssessmentNote: Coding = {
  code: '64064-9',
  display: 'Pastoral care Hospital Assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CaseManagerHospitalInitialAssessmentNote: Coding = {
  code: '64065-6',
  display: 'Case manager Hospital Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryMedicalStudentHospitalAdmissionEvaluationNote: Coding = {
  code: '64066-4',
  display: 'Surgery Medical student Hospital Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryMedicalStudentHospitalProgressNote: Coding = {
  code: '64067-2',
  display: 'Surgery Medical student Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryMedicalStudentHospitalConsultNote: Coding = {
  code: '64068-0',
  display: 'Surgery Medical student Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CriticalCareMedicinePhysicianAttendingHospitalNote: Coding = {
  code: '64069-8',
  display: 'Critical care medicine Physician attending Hospital Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CriticalCareMedicineMedicalStudentHospitalAdmissionEvaluationNote: Coding = {
  code: '64070-6',
  display: 'Critical care medicine Medical student Hospital Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CriticalCareMedicineMedicalStudentHospitalProgressNote: Coding = {
  code: '64071-4',
  display: 'Critical care medicine Medical student Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CriticalCareMedicineMedicalStudentHospitalConsultNote: Coding = {
  code: '64072-2',
  display: 'Critical care medicine Medical student Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSurgeryPhysicianAttendingHospitalNote: Coding = {
  code: '64073-0',
  display: 'Thoracic surgery Physician attending Hospital Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSurgeryMedicalStudentHospitalAdmissionEvaluationNote: Coding = {
  code: '64074-8',
  display: 'Thoracic surgery Medical student Hospital Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSurgeryMedicalStudentHospitalProgressNote: Coding = {
  code: '64075-5',
  display: 'Thoracic surgery Medical student Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSurgeryMedicalStudentHospitalConsultNote: Coding = {
  code: '64076-3',
  display: 'Thoracic surgery Medical student Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryPhysicianAttendingHospitalNote: Coding = {
  code: '64077-1',
  display: 'Pulmonary Physician attending Hospital Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryMedicalStudentHospitalAdmissionEvaluationNote: Coding = {
  code: '64078-9',
  display: 'Pulmonary Medical student Hospital Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryMedicalStudentHospitalProgressNote: Coding = {
  code: '64079-7',
  display: 'Pulmonary Medical student Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryMedicalStudentHospitalConsultNote: Coding = {
  code: '64080-5',
  display: 'Pulmonary Medical student Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertIndexingAdverseReaction: Coding = {
  code: '64123-3',
  display: 'FDA package insert Indexing - adverse reaction',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertIndexingSubstance: Coding = {
  code: '64124-1',
  display: 'FDA package insert Indexing - substance',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsLeftFluoroscopicAngiogramWContrast: Coding = {
  code: '64140-7',
  display: 'Renal vessels - left Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsRightFluoroscopicAngiogramWContrast: Coding = {
  code: '64141-5',
  display: 'Renal vessels - right Fluoroscopic angiogram W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalEvaluationAndManagementOfSmokingCessation: Coding = {
  code: '64142-3',
  display: 'Hospital Evaluation and management of smoking cessation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ReadinessForDutyAssessment: Coding = {
  code: '64284-3',
  display: 'Readiness for duty assessment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MedicalHistoryScreeningForm: Coding = {
  code: '64285-0',
  display: 'Medical history screening form',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrescriptionForEyewear: Coding = {
  code: '64288-4',
  display: 'Prescription for eyewear',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HealthRecordCoverSheet: Coding = {
  code: '64289-2',
  display: 'Health record cover sheet',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HealthInsuranceCard: Coding = {
  code: '64290-0',
  display: 'Health insurance card',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HealthInsuranceRelatedForm: Coding = {
  code: '64291-8',
  display: 'Health insurance-related form',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ReleaseOfInformationConsent: Coding = {
  code: '64292-6',
  display: 'Release of information consent',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProcedureConsent: Coding = {
  code: '64293-4',
  display: 'Procedure consent',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ReadinessForDutyLetter: Coding = {
  code: '64294-2',
  display: 'Readiness for duty letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NursePlanOfCareNote: Coding = {
  code: '64295-9',
  display: 'Nurse Plan of care note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PersonalHealthMonitoringReportAutomated: Coding = {
  code: '64296-7',
  display: 'Personal health monitoring report Automated',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DeathCertificate: Coding = {
  code: '64297-5',
  display: 'Death certificate',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PowerOfAttorney: Coding = {
  code: '64298-3',
  display: 'Power of attorney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LegalDocument: Coding = {
  code: '64299-1',
  display: 'Legal document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrganDonationConsent: Coding = {
  code: '64300-7',
  display: 'Organ donation consent',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForPlacementOfNeedleInUnspecifiedBodyRegion: Coding = {
  code: '64993-9',
  display: 'US Guidance for placement of needle in Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammaryArteryInternalLeftFluoroscopicAngiogramWContrastIA: Coding = {
  code: '64995-4',
  display: 'Mammary artery.internal - left Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungLeftXRayWContrastIntrabronchial: Coding = {
  code: '64996-2',
  display: 'Lung - left X-ray W contrast intrabronchial',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LungRightXRayWContrastIntrabronchial: Coding = {
  code: '64997-0',
  display: 'Lung - right X-ray W contrast intrabronchial',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForCatheterizationOfFallopianTubeLeftTranscervical: Coding = {
  code: '64998-8',
  display: 'Fluoroscopy Guidance for catheterization of Fallopian tube - left-- transcervical',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForCatheterizationOfFallopianTubeRightTranscervical: Coding = {
  code: '64999-6',
  display: 'Fluoroscopy Guidance for catheterization of Fallopian tube -right-- transcervical',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammaryArteryInternalRightFluoroscopicAngiogramWContrastIA: Coding = {
  code: '65000-2',
  display: 'Mammary artery.internal - right Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInArteryLeft: Coding = {
  code: '65797-3',
  display: 'Fluoroscopic angiogram Guidance for placement of stent in Artery - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInArteryRight: Coding = {
  code: '65798-1',
  display: 'Fluoroscopic angiogram Guidance for placement of stent in Artery - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralFluoroscopyViewForCystExamination: Coding = {
  code: '65799-9',
  display: 'Kidney - bilateral Fluoroscopy View for cyst examination',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyLeftFluoroscopyViewForCystExamination: Coding = {
  code: '65800-5',
  display: 'Kidney - left Fluoroscopy View for cyst examination',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyRightFluoroscopyViewForCystExamination: Coding = {
  code: '65801-3',
  display: 'Kidney - right Fluoroscopy View for cyst examination',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SagittalSinusAndJugularVeinsLeftFluoroscopicAngiogramWContrastIV: Coding = {
  code: '65802-1',
  display: 'Sagittal sinus and Jugular veins - left Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SagittalSinusVeinLeftFluoroscopicAngiogramWContrastIV: Coding = {
  code: '65803-9',
  display: 'Sagittal sinus vein - left Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SagittalSinusVeinRightFluoroscopicAngiogramWContrastIV: Coding = {
  code: '65804-7',
  display: 'Sagittal sinus vein - right Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SagittalSinusAndJugularVeinsRightFluoroscopicAngiogramWContrastIV: Coding = {
  code: '65805-4',
  display: 'Sagittal sinus and Jugular veins - right Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InhalationChallengeTestReportDocumentWMethacholineInhaled: Coding = {
  code: '65806-2',
  display: 'Inhalation challenge test report Document --W methacholine inhaled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertLotDistributionData: Coding = {
  code: '66105-8',
  display: 'FDA package insert Lot distribution data',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPharmacogenomicsSection: Coding = {
  code: '66106-6',
  display: 'FDA package insert Pharmacogenomics section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EMSPatientCareReportVersion31DocumentNEMSIS: Coding = {
  code: '67796-3',
  display: 'EMS patient care report - version 3.1 Document NEMSIS',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdmissionEvaluationNote: Coding = {
  code: '67851-6',
  display: 'Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalAdmissionEvaluationNote: Coding = {
  code: '67852-4',
  display: 'Hospital Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeriatricMedicineEducationNote: Coding = {
  code: '67854-0',
  display: 'Geriatric medicine Education note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OutpatientEducationNote: Coding = {
  code: '67855-7',
  display: 'Outpatient Education note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NursingFacilityHistoryAndPhysicalNote: Coding = {
  code: '67856-5',
  display: 'Nursing facility History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PostoperativeEvaluationAndManagementNote: Coding = {
  code: '67860-7',
  display: 'Postoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OphthalmologyPostoperativeEvaluationAndManagementNote: Coding = {
  code: '67861-5',
  display: 'Ophthalmology Postoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PreoperativeEvaluationAndManagementNote: Coding = {
  code: '67862-3',
  display: 'Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OutpatientSupervisoryNote: Coding = {
  code: '67865-6',
  display: 'Outpatient Supervisory note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PastoralCareHospitalConsultNote: Coding = {
  code: '68469-6',
  display: 'Pastoral care Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RespiratoryTherapyHospitalInitialAssessmentNote: Coding = {
  code: '68470-4',
  display: 'Respiratory therapy Hospital Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyHospitalAdmissionEvaluationNote: Coding = {
  code: '68471-2',
  display: 'Cardiology Hospital Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyHospitalProgressNote: Coding = {
  code: '68472-0',
  display: 'Cardiology Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CriticalCareMedicinePhysicianAttendingHospitalProgressNote: Coding = {
  code: '68473-8',
  display: 'Critical care medicine Physician attending Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicianHospitalRestraintNote: Coding = {
  code: '68474-6',
  display: 'Physician Hospital Restraint note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeneralMedicinePhysicianAttendingHospitalProgressNote: Coding = {
  code: '68475-3',
  display: 'General medicine Physician attending Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseHospitalRestraintNote: Coding = {
  code: '68476-1',
  display: 'Nurse Hospital Restraint note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseHospitalEducationNote: Coding = {
  code: '68477-9',
  display: 'Nurse Hospital Education note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryPhysicianAttendingHospitalProgressNote: Coding = {
  code: '68478-7',
  display: 'Pulmonary Physician attending Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RespiratoryTherapyHospitalProgressNote: Coding = {
  code: '68479-5',
  display: 'Respiratory therapy Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryPhysicianAttendingHospitalProgressNote: Coding = {
  code: '68480-3',
  display: 'Surgery Physician attending Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThoracicSurgeryPhysicianAttendingHospitalProgressNote: Coding = {
  code: '68481-1',
  display: 'Thoracic surgery Physician attending Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NurseHospitalTransferSummaryNote: Coding = {
  code: '68482-9',
  display: 'Nurse Hospital Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyMedicalStudentHospitalAdmissionEvaluationNote: Coding = {
  code: '68483-7',
  display: 'Cardiology Medical student Hospital Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyPhysicianAttendingHospitalProgressNote: Coding = {
  code: '68484-5',
  display: 'Cardiology Physician attending Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyMedicalStudentHospitalProgressNote: Coding = {
  code: '68485-2',
  display: 'Cardiology Medical student Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CardiologyMedicalStudentHospitalConsultNote: Coding = {
  code: '68486-0',
  display: 'Cardiology Medical student Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DermatologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68550-3',
  display: 'Dermatology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DermatologyHospitalConsultNote: Coding = {
  code: '68551-1',
  display: 'Dermatology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EmergencyMedicineEmergencyDepartmentAdmissionEvaluationNote: Coding = {
  code: '68552-9',
  display: 'Emergency medicine Emergency department Admission evaluation note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HematologyPlusMedicalOncologyInitialAssessmentNote: Coding = {
  code: '68553-7',
  display: 'Hematology+Medical Oncology Initial assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HematologyPlusMedicalOncologyProgressNote: Coding = {
  code: '68554-5',
  display: 'Hematology+Medical Oncology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HematologyPlusMedicalOncologyHospitalLetter: Coding = {
  code: '68555-2',
  display: 'Hematology+Medical Oncology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyDiagnosticStudyNote: Coding = {
  code: '68556-0',
  display: 'Neurology Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ObstetricsAndGynecologyDiagnosticStudyNote: Coding = {
  code: '68557-8',
  display: 'Obstetrics and Gynecology Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ObstetricsAndGynecologyDischargeSummary: Coding = {
  code: '68558-6',
  display: 'Obstetrics and Gynecology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ObstetricsAndGynecologyHistoryAndPhysicalNote: Coding = {
  code: '68560-2',
  display: 'Obstetrics and Gynecology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ObstetricsAndGynecologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68562-8',
  display: 'Obstetrics and Gynecology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ObstetricsAndGynecologyProcedureNote: Coding = {
  code: '68563-6',
  display: 'Obstetrics and Gynecology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ObstetricsAndGynecologyProgressNote: Coding = {
  code: '68564-4',
  display: 'Obstetrics and Gynecology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ObstetricsAndGynecologyTransferSummaryNote: Coding = {
  code: '68565-1',
  display: 'Obstetrics and Gynecology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ObstetricsAndGynecologyHospitalConsultNote: Coding = {
  code: '68566-9',
  display: 'Obstetrics and Gynecology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ObstetricsAndGynecologyHospitalLetter: Coding = {
  code: '68567-7',
  display: 'Obstetrics and Gynecology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalTherapyTransferSummaryNote: Coding = {
  code: '68569-3',
  display: 'Occupational therapy Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalTherapyHospitalConsultNote: Coding = {
  code: '68570-1',
  display: 'Occupational therapy Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalTherapyHospitalLetter: Coding = {
  code: '68571-9',
  display: 'Occupational therapy Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OphthalmologyDischargeSummary: Coding = {
  code: '68572-7',
  display: 'Ophthalmology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OphthalmologyHistoryAndPhysicalNote: Coding = {
  code: '68573-5',
  display: 'Ophthalmology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OphthalmologyProgressNote: Coding = {
  code: '68574-3',
  display: 'Ophthalmology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OphthalmologyHospitalConsultNote: Coding = {
  code: '68575-0',
  display: 'Ophthalmology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OphthalmologyHospitalLetter: Coding = {
  code: '68576-8',
  display: 'Ophthalmology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrthopaedicSurgeryDiagnosticStudyNote: Coding = {
  code: '68577-6',
  display: 'Orthopaedic surgery Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrthopaedicSurgeryDischargeSummary: Coding = {
  code: '68578-4',
  display: 'Orthopaedic surgery Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrthopaedicSurgeryHistoryAndPhysicalNote: Coding = {
  code: '68580-0',
  display: 'Orthopaedic surgery History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrthopaedicSurgeryPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68581-8',
  display: 'Orthopaedic surgery Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrthopaedicSurgeryProgressNote: Coding = {
  code: '68582-6',
  display: 'Orthopaedic surgery Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrthopaedicSurgeryTransferSummaryNote: Coding = {
  code: '68583-4',
  display: 'Orthopaedic surgery Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OrthopaedicSurgeryHospitalLetter: Coding = {
  code: '68585-9',
  display: 'Orthopaedic surgery Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PharmacyHospitalConsultNote: Coding = {
  code: '68586-7',
  display: 'Pharmacy Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PharmacyHospitalMedicationManagementNote: Coding = {
  code: '68587-5',
  display: 'Pharmacy Hospital Medication management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhysicalTherapyHospitalConsultNote: Coding = {
  code: '68590-9',
  display: 'Physical therapy Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlasticSurgeryDischargeSummary: Coding = {
  code: '68591-7',
  display: 'Plastic surgery Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlasticSurgeryHistoryAndPhysicalNote: Coding = {
  code: '68592-5',
  display: 'Plastic surgery History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlasticSurgeryLetter: Coding = {
  code: '68593-3',
  display: 'Plastic surgery Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlasticSurgeryPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68594-1',
  display: 'Plastic surgery Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlasticSurgeryProgressNote: Coding = {
  code: '68595-8',
  display: 'Plastic surgery Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlasticSurgeryTransferSummaryNote: Coding = {
  code: '68596-6',
  display: 'Plastic surgery Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlasticSurgeryHospitalConsultNote: Coding = {
  code: '68597-4',
  display: 'Plastic surgery Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PlasticSurgeryHospitalLetter: Coding = {
  code: '68598-2',
  display: 'Plastic surgery Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychiatryHistoryAndPhysicalNote: Coding = {
  code: '68599-0',
  display: 'Psychiatry History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PsychiatryOutpatientNote: Coding = {
  code: '68601-4',
  display: 'Psychiatry Outpatient Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiationOncologySummaryNote: Coding = {
  code: '68602-2',
  display: 'Radiation oncology Summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiationOncologyHospitalSummaryNote: Coding = {
  code: '68603-0',
  display: 'Radiation oncology Hospital Summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiologyDiagnosticStudyNote: Coding = {
  code: '68604-8',
  display: 'Radiology Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RecreationalTherapyHospitalEducationNote: Coding = {
  code: '68605-5',
  display: 'Recreational therapy Hospital Education note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgeryHospitalPostoperativeEvaluationAndManagementNote: Coding = {
  code: '68606-3',
  display: 'Surgery Hospital Postoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ProgressLetter: Coding = {
  code: '68607-1',
  display: 'Progress letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SummaryNote: Coding = {
  code: '68608-9',
  display: 'Summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalLetter: Coding = {
  code: '68609-7',
  display: 'Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalPostoperativeEvaluationAndManagementNote: Coding = {
  code: '68610-5',
  display: 'Hospital Postoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdolescentMedicineDiagnosticStudyNote: Coding = {
  code: '68611-3',
  display: 'Adolescent medicine Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdolescentMedicineDischargeSummary: Coding = {
  code: '68612-1',
  display: 'Adolescent medicine Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdolescentMedicineHistoryAndPhysicalNote: Coding = {
  code: '68614-7',
  display: 'Adolescent medicine History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdolescentMedicineNote: Coding = {
  code: '68615-4',
  display: 'Adolescent medicine Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdolescentMedicinePreoperativeEvaluationAndManagementNote: Coding = {
  code: '68616-2',
  display: 'Adolescent medicine Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdolescentMedicineProgressNote: Coding = {
  code: '68617-0',
  display: 'Adolescent medicine Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdolescentMedicineTransferSummaryNote: Coding = {
  code: '68618-8',
  display: 'Adolescent medicine Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdolescentMedicineHospitalConsultNote: Coding = {
  code: '68619-6',
  display: 'Adolescent medicine Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdolescentMedicineHospitalLetter: Coding = {
  code: '68620-4',
  display: 'Adolescent medicine Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdvancedHeartFailureAndTransplantCardiologyNote: Coding = {
  code: '68621-2',
  display: 'Advanced heart failure and transplant cardiology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdvancedHeartFailureAndTransplantCardiologyHistoryAndPhysicalNote: Coding = {
  code: '68622-0',
  display: 'Advanced heart failure and transplant cardiology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdvancedHeartFailureAndTransplantCardiologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68623-8',
  display: 'Advanced heart failure and transplant cardiology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdvancedHeartFailureAndTransplantCardiologyHospitalLetter: Coding = {
  code: '68624-6',
  display: 'Advanced heart failure and transplant cardiology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AllergyAndImmunologyDiagnosticStudyNote: Coding = {
  code: '68625-3',
  display: 'Allergy and immunology Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AllergyAndImmunologyDischargeSummary: Coding = {
  code: '68626-1',
  display: 'Allergy and immunology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AllergyAndImmunologyHistoryAndPhysicalNote: Coding = {
  code: '68628-7',
  display: 'Allergy and immunology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AllergyAndImmunologyNote: Coding = {
  code: '68629-5',
  display: 'Allergy and immunology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AllergyAndImmunologyProcedureNote: Coding = {
  code: '68630-3',
  display: 'Allergy and immunology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AllergyAndImmunologyProgressNote: Coding = {
  code: '68631-1',
  display: 'Allergy and immunology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AllergyAndImmunologyTransferSummaryNote: Coding = {
  code: '68632-9',
  display: 'Allergy and immunology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AllergyAndImmunologyHospitalConsultNote: Coding = {
  code: '68633-7',
  display: 'Allergy and immunology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AllergyAndImmunologyHospitalLetter: Coding = {
  code: '68634-5',
  display: 'Allergy and immunology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AudiologyDiagnosticStudyNote: Coding = {
  code: '68635-2',
  display: 'Audiology Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AudiologyNote: Coding = {
  code: '68636-0',
  display: 'Audiology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AudiologyHistoryAndPhysicalNote: Coding = {
  code: '68637-8',
  display: 'Audiology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AudiologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68638-6',
  display: 'Audiology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AudiologyHospitalConsultNote: Coding = {
  code: '68639-4',
  display: 'Audiology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AudiologyHospitalDiagnosticStudyNote: Coding = {
  code: '68640-2',
  display: 'Audiology Hospital Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChildAndAdolescentPsychiatryDiagnosticStudyNote: Coding = {
  code: '68641-0',
  display: 'Child and adolescent psychiatry Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChildAndAdolescentPsychiatryDischargeSummary: Coding = {
  code: '68642-8',
  display: 'Child and adolescent psychiatry Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChildAndAdolescentPsychiatryHistoryAndPhysicalNote: Coding = {
  code: '68644-4',
  display: 'Child and adolescent psychiatry History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChildAndAdolescentPsychiatryNote: Coding = {
  code: '68645-1',
  display: 'Child and adolescent psychiatry Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChildAndAdolescentPsychiatryProgressNote: Coding = {
  code: '68646-9',
  display: 'Child and adolescent psychiatry Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChildAndAdolescentPsychiatryTransferSummaryNote: Coding = {
  code: '68647-7',
  display: 'Child and adolescent psychiatry Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChildAndAdolescentPsychiatryHospitalConsultNote: Coding = {
  code: '68648-5',
  display: 'Child and adolescent psychiatry Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChildAndAdolescentPsychiatryHospitalLetter: Coding = {
  code: '68649-3',
  display: 'Child and adolescent psychiatry Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalBiochemicalGeneticsNote: Coding = {
  code: '68650-1',
  display: 'Clinical biochemical genetics Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalBiochemicalGeneticsHospitalConsultNote: Coding = {
  code: '68651-9',
  display: 'Clinical biochemical genetics Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalGeneticsDiagnosticStudyNote: Coding = {
  code: '68652-7',
  display: 'Clinical genetics Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalGeneticsDischargeSummary: Coding = {
  code: '68653-5',
  display: 'Clinical genetics Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalGeneticsHistoryAndPhysicalNote: Coding = {
  code: '68655-0',
  display: 'Clinical genetics History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalGeneticsNote: Coding = {
  code: '68656-8',
  display: 'Clinical genetics Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalGeneticsPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68657-6',
  display: 'Clinical genetics Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalGeneticsProcedureNote: Coding = {
  code: '68658-4',
  display: 'Clinical genetics procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalGeneticsProgressNote: Coding = {
  code: '68659-2',
  display: 'Clinical genetics Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalGeneticsTransferSummaryNote: Coding = {
  code: '68660-0',
  display: 'Clinical genetics Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalGeneticsHospitalConsultNote: Coding = {
  code: '68661-8',
  display: 'Clinical genetics Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalGeneticsHospitalLetter: Coding = {
  code: '68662-6',
  display: 'Clinical genetics Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DevelopmentalBehavioralPediatricsDischargeSummary: Coding = {
  code: '68663-4',
  display: 'Developmental-behavioral pediatrics Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DevelopmentalBehavioralPediatricsHistoryAndPhysicalNote: Coding = {
  code: '68665-9',
  display: 'Developmental-behavioral pediatrics History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DevelopmentalBehavioralPediatricsNote: Coding = {
  code: '68666-7',
  display: 'Developmental-behavioral pediatrics Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DevelopmentalBehavioralPediatricsProcedureNote: Coding = {
  code: '68667-5',
  display: 'Developmental-behavioral pediatrics procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DevelopmentalBehavioralPediatricsProgressNote: Coding = {
  code: '68668-3',
  display: 'Developmental-behavioral pediatrics Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DevelopmentalBehavioralPediatricsTransferSummaryNote: Coding = {
  code: '68669-1',
  display: 'Developmental-behavioral pediatrics Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DevelopmentalBehavioralPediatricsHospitalConsultNote: Coding = {
  code: '68670-9',
  display: 'Developmental-behavioral pediatrics Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DevelopmentalBehavioralPediatricsHospitalLetter: Coding = {
  code: '68671-7',
  display: 'Developmental-behavioral pediatrics Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_GeriatricMedicineSkilledNursingFacilityNote: Coding = {
  code: '68672-5',
  display: 'Geriatric medicine Skilled nursing facility Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MultiSpecialtyProgramDiagnosticStudyNote: Coding = {
  code: '68673-3',
  display: 'Multi-specialty program Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MultiSpecialtyProgramDischargeSummary: Coding = {
  code: '68674-1',
  display: 'Multi-specialty program Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MultiSpecialtyProgramHistoryAndPhysicalNote: Coding = {
  code: '68676-6',
  display: 'Multi-specialty program History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MultiSpecialtyProgramNote: Coding = {
  code: '68677-4',
  display: 'Multi-specialty program Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MultiSpecialtyProgramPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68678-2',
  display: 'Multi-specialty program Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MultiSpecialtyProgramProgressNote: Coding = {
  code: '68679-0',
  display: 'Multi-specialty program Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MultiSpecialtyProgramTransferSummaryNote: Coding = {
  code: '68680-8',
  display: 'Multi-specialty program Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MultiSpecialtyProgramHospitalConsultNote: Coding = {
  code: '68681-6',
  display: 'Multi-specialty program Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MultiSpecialtyProgramHospitalLetter: Coding = {
  code: '68682-4',
  display: 'Multi-specialty program Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeonatalPerinatalMedicineHistoryAndPhysicalNote: Coding = {
  code: '68683-2',
  display: 'Neonatal perinatal medicine History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeonatalPerinatalMedicineLetter: Coding = {
  code: '68684-0',
  display: 'Neonatal perinatal medicine Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeonatalPerinatalMedicineHospitalConsultNote: Coding = {
  code: '68685-7',
  display: 'Neonatal perinatal medicine Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeonatalPerinatalMedicineHospitalLetter: Coding = {
  code: '68686-5',
  display: 'Neonatal perinatal medicine Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologicalSurgeryDiagnosticStudyNote: Coding = {
  code: '68687-3',
  display: 'Neurological surgery Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologicalSurgeryDischargeSummary: Coding = {
  code: '68688-1',
  display: 'Neurological surgery Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologicalSurgeryHistoryAndPhysicalNote: Coding = {
  code: '68690-7',
  display: 'Neurological surgery History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologicalSurgeryPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68691-5',
  display: 'Neurological surgery Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologicalSurgeryProcedureNote: Coding = {
  code: '68692-3',
  display: 'Neurological surgery procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologicalSurgeryProgressNote: Coding = {
  code: '68693-1',
  display: 'Neurological surgery Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologicalSurgeryHospitalConsultNote: Coding = {
  code: '68694-9',
  display: 'Neurological surgery Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologicalSurgeryHospitalLetter: Coding = {
  code: '68695-6',
  display: 'Neurological surgery Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyDiagnosticStudyNote: Coding = {
  code: '68696-4',
  display: 'Neurology with special qualifications in child neurology Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyDischargeSummary: Coding = {
  code: '68697-2',
  display: 'Neurology with special qualifications in child neurology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyHistoryAndPhysicalNote: Coding = {
  code: '68699-8',
  display: 'Neurology with special qualifications in child neurology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyNote: Coding = {
  code: '68700-4',
  display: 'Neurology with special qualifications in child neurology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68701-2',
  display: 'Neurology with special qualifications in child neurology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyProcedureNote: Coding = {
  code: '68702-0',
  display: 'Neurology with special qualifications in child neurology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyProgressNote: Coding = {
  code: '68703-8',
  display: 'Neurology with special qualifications in child neurology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyTransferSummaryNote: Coding = {
  code: '68704-6',
  display: 'Neurology with special qualifications in child neurology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyHospitalConsultNote: Coding = {
  code: '68705-3',
  display: 'Neurology with special qualifications in child neurology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyHospitalDiagnosticStudyNote: Coding = {
  code: '68706-1',
  display: 'Neurology with special qualifications in child neurology Hospital Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyHospitalLetter: Coding = {
  code: '68707-9',
  display: 'Neurology with special qualifications in child neurology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PainMedicineDiagnosticStudyNote: Coding = {
  code: '68708-7',
  display: 'Pain medicine Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PainMedicineDischargeSummary: Coding = {
  code: '68709-5',
  display: 'Pain medicine Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PainMedicineHistoryAndPhysicalNote: Coding = {
  code: '68711-1',
  display: 'Pain medicine History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PainMedicinePreoperativeEvaluationAndManagementNote: Coding = {
  code: '68713-7',
  display: 'Pain medicine Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PainMedicineProcedureNote: Coding = {
  code: '68714-5',
  display: 'Pain medicine procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PainMedicineTransferSummaryNote: Coding = {
  code: '68715-2',
  display: 'Pain medicine Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PainMedicineHospitalConsultNote: Coding = {
  code: '68716-0',
  display: 'Pain medicine Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PainMedicineHospitalLetter: Coding = {
  code: '68717-8',
  display: 'Pain medicine Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricCardiologyDiagnosticStudyNote: Coding = {
  code: '68718-6',
  display: 'Pediatric cardiology Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricCardiologyDischargeSummary: Coding = {
  code: '68719-4',
  display: 'Pediatric cardiology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricCardiologyHistoryAndPhysicalNote: Coding = {
  code: '68721-0',
  display: 'Pediatric cardiology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricCardiologyNote: Coding = {
  code: '68722-8',
  display: 'Pediatric cardiology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricCardiologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68723-6',
  display: 'Pediatric cardiology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricCardiologyProcedureNote: Coding = {
  code: '68724-4',
  display: 'Pediatric cardiology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricCardiologyProgressNote: Coding = {
  code: '68725-1',
  display: 'Pediatric cardiology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricCardiologyTransferSummaryNote: Coding = {
  code: '68726-9',
  display: 'Pediatric cardiology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricCardiologyHospitalConsultNote: Coding = {
  code: '68727-7',
  display: 'Pediatric cardiology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricCardiologyHospitalLetter: Coding = {
  code: '68728-5',
  display: 'Pediatric cardiology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricCriticalCareMedicineHospitalProcedureNote: Coding = {
  code: '68729-3',
  display: 'Pediatric critical care medicine Hospital procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricDermatologyHistoryAndPhysicalNote: Coding = {
  code: '68731-9',
  display: 'Pediatric dermatology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricDermatologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68732-7',
  display: 'Pediatric dermatology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricEndocrinologyDischargeSummary: Coding = {
  code: '68733-5',
  display: 'Pediatric endocrinology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricEndocrinologyHistoryAndPhysicalNote: Coding = {
  code: '68735-0',
  display: 'Pediatric endocrinology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricEndocrinologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68736-8',
  display: 'Pediatric endocrinology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricEndocrinologyTransferSummaryNote: Coding = {
  code: '68737-6',
  display: 'Pediatric endocrinology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricGastroenterologyDischargeSummary: Coding = {
  code: '68738-4',
  display: 'Pediatric gastroenterology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricGastroenterologyHistoryAndPhysicalNote: Coding = {
  code: '68740-0',
  display: 'Pediatric gastroenterology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricGastroenterologyNote: Coding = {
  code: '68741-8',
  display: 'Pediatric gastroenterology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricGastroenterologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68742-6',
  display: 'Pediatric gastroenterology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricGastroenterologyProcedureNote: Coding = {
  code: '68743-4',
  display: 'Pediatric gastroenterology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricGastroenterologyProgressNote: Coding = {
  code: '68744-2',
  display: 'Pediatric gastroenterology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricGastroenterologyTransferSummaryNote: Coding = {
  code: '68745-9',
  display: 'Pediatric gastroenterology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricGastroenterologyHospitalConsultNote: Coding = {
  code: '68746-7',
  display: 'Pediatric gastroenterology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricGastroenterologyHospitalLetter: Coding = {
  code: '68747-5',
  display: 'Pediatric gastroenterology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricHematologyOncologyDiagnosticStudyNote: Coding = {
  code: '68748-3',
  display: 'Pediatric hematology-oncology Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricHematologyOncologyDischargeSummary: Coding = {
  code: '68749-1',
  display: 'Pediatric hematology-oncology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricHematologyOncologyHistoryAndPhysicalNote: Coding = {
  code: '68751-7',
  display: 'Pediatric hematology-oncology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricHematologyOncologyNote: Coding = {
  code: '68752-5',
  display: 'Pediatric hematology-oncology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricHematologyOncologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68753-3',
  display: 'Pediatric hematology-oncology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricHematologyOncologyProcedureNote: Coding = {
  code: '68754-1',
  display: 'Pediatric hematology-oncology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricHematologyOncologyProgressNote: Coding = {
  code: '68755-8',
  display: 'Pediatric hematology-oncology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricHematologyOncologyTransferSummaryNote: Coding = {
  code: '68756-6',
  display: 'Pediatric hematology-oncology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricHematologyOncologyHospitalConsultNote: Coding = {
  code: '68757-4',
  display: 'Pediatric hematology-oncology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricHematologyOncologyHospitalLetter: Coding = {
  code: '68758-2',
  display: 'Pediatric hematology-oncology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricInfectiousDiseasesHistoryAndPhysicalNote: Coding = {
  code: '68760-8',
  display: 'Pediatric infectious diseases History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricInfectiousDiseasesNote: Coding = {
  code: '68761-6',
  display: 'Pediatric infectious diseases Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricInfectiousDiseasesPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68762-4',
  display: 'Pediatric infectious diseases Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricInfectiousDiseasesProgressNote: Coding = {
  code: '68763-2',
  display: 'Pediatric infectious diseases Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricInfectiousDiseasesTransferSummaryNote: Coding = {
  code: '68764-0',
  display: 'Pediatric infectious diseases Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricInfectiousDiseasesHospitalConsultNote: Coding = {
  code: '68765-7',
  display: 'Pediatric infectious diseases Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricInfectiousDiseasesHospitalLetter: Coding = {
  code: '68766-5',
  display: 'Pediatric infectious diseases Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricNephrologyDiagnosticStudyNote: Coding = {
  code: '68767-3',
  display: 'Pediatric nephrology Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricNephrologyDischargeSummary: Coding = {
  code: '68768-1',
  display: 'Pediatric nephrology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricNephrologyHistoryAndPhysicalNote: Coding = {
  code: '68770-7',
  display: 'Pediatric nephrology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricNephrologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68771-5',
  display: 'Pediatric nephrology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricNephrologyTransferSummaryNote: Coding = {
  code: '68772-3',
  display: 'Pediatric nephrology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricOtolaryngologyDischargeSummary: Coding = {
  code: '68773-1',
  display: 'Pediatric otolaryngology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricOtolaryngologyHistoryAndPhysicalNote: Coding = {
  code: '68775-6',
  display: 'Pediatric otolaryngology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricOtolaryngologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68776-4',
  display: 'Pediatric otolaryngology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricOtolaryngologyTransferSummaryNote: Coding = {
  code: '68777-2',
  display: 'Pediatric otolaryngology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricPulmonologyDiagnosticStudyNote: Coding = {
  code: '68778-0',
  display: 'Pediatric pulmonology Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricPulmonologyDischargeSummary: Coding = {
  code: '68779-8',
  display: 'Pediatric pulmonology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricPulmonologyHistoryAndPhysicalNote: Coding = {
  code: '68781-4',
  display: 'Pediatric pulmonology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricPulmonologyNote: Coding = {
  code: '68782-2',
  display: 'Pediatric pulmonology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricPulmonologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68783-0',
  display: 'Pediatric pulmonology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricPulmonologyProcedureNote: Coding = {
  code: '68784-8',
  display: 'Pediatric pulmonology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricPulmonologyProgressNote: Coding = {
  code: '68785-5',
  display: 'Pediatric pulmonology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricPulmonologyTransferSummaryNote: Coding = {
  code: '68786-3',
  display: 'Pediatric pulmonology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricPulmonologyHospitalConsultNote: Coding = {
  code: '68787-1',
  display: 'Pediatric pulmonology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricPulmonologyHospitalDiagnosticStudyNote: Coding = {
  code: '68788-9',
  display: 'Pediatric pulmonology Hospital Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricPulmonologyHospitalLetter: Coding = {
  code: '68789-7',
  display: 'Pediatric pulmonology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricRheumatologyHistoryAndPhysicalNote: Coding = {
  code: '68791-3',
  display: 'Pediatric rheumatology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricRheumatologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68792-1',
  display: 'Pediatric rheumatology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricRheumatologyTransferSummaryNote: Coding = {
  code: '68793-9',
  display: 'Pediatric rheumatology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricSurgeryDiagnosticStudyNote: Coding = {
  code: '68794-7',
  display: 'Pediatric surgery Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricSurgeryDischargeSummary: Coding = {
  code: '68795-4',
  display: 'Pediatric surgery Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricSurgeryHistoryAndPhysicalNote: Coding = {
  code: '68797-0',
  display: 'Pediatric surgery History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricSurgeryPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68798-8',
  display: 'Pediatric surgery Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricSurgeryProcedureNote: Coding = {
  code: '68799-6',
  display: 'Pediatric surgery procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricSurgeryProgressNote: Coding = {
  code: '68800-2',
  display: 'Pediatric surgery Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricSurgeryTransferSummaryNote: Coding = {
  code: '68801-0',
  display: 'Pediatric surgery Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricSurgeryHospitalConsultNote: Coding = {
  code: '68802-8',
  display: 'Pediatric surgery Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricSurgeryHospitalLetter: Coding = {
  code: '68803-6',
  display: 'Pediatric surgery Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricUrologyDiagnosticStudyNote: Coding = {
  code: '68804-4',
  display: 'Pediatric urology Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricUrologyDischargeSummary: Coding = {
  code: '68805-1',
  display: 'Pediatric urology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricUrologyHistoryAndPhysicalNote: Coding = {
  code: '68807-7',
  display: 'Pediatric urology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricUrologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68808-5',
  display: 'Pediatric urology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricUrologyProcedureNote: Coding = {
  code: '68809-3',
  display: 'Pediatric urology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricUrologyProgressNote: Coding = {
  code: '68810-1',
  display: 'Pediatric urology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricUrologyTransferSummaryNote: Coding = {
  code: '68811-9',
  display: 'Pediatric urology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricUrologyHospitalConsultNote: Coding = {
  code: '68812-7',
  display: 'Pediatric urology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricUrologyHospitalLetter: Coding = {
  code: '68813-5',
  display: 'Pediatric urology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsAssessmentNote: Coding = {
  code: '68814-3',
  display: 'Pediatrics Assessment note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsDischargeSummary: Coding = {
  code: '68815-0',
  display: 'Pediatrics Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsHistoryAndPhysicalNote: Coding = {
  code: '68817-6',
  display: 'Pediatrics History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsNote: Coding = {
  code: '68818-4',
  display: 'Pediatrics Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68819-2',
  display: 'Pediatrics Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsProcedureNote: Coding = {
  code: '68820-0',
  display: 'Pediatrics procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsHospitalConsultNote: Coding = {
  code: '68821-8',
  display: 'Pediatrics Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsHospitalDiagnosticStudyNote: Coding = {
  code: '68822-6',
  display: 'Pediatrics Hospital Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsHospitalDischargeSummary: Coding = {
  code: '68823-4',
  display: 'Pediatrics Hospital Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsHospitalHistoryAndPhysicalNote: Coding = {
  code: '68825-9',
  display: 'Pediatrics Hospital History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsHospitalLetter: Coding = {
  code: '68826-7',
  display: 'Pediatrics Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsHospitalNote: Coding = {
  code: '68827-5',
  display: 'Pediatrics Hospital Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsHospitalPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68828-3',
  display: 'Pediatrics Hospital Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsHospitalProcedureNote: Coding = {
  code: '68829-1',
  display: 'Pediatrics Hospital procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsHospitalProgressNote: Coding = {
  code: '68830-9',
  display: 'Pediatrics Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrimaryCareDischargeSummary: Coding = {
  code: '68831-7',
  display: 'Primary care Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrimaryCareHistoryAndPhysicalNote: Coding = {
  code: '68833-3',
  display: 'Primary care History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrimaryCareNote: Coding = {
  code: '68834-1',
  display: 'Primary care Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrimaryCarePreoperativeEvaluationAndManagementNote: Coding = {
  code: '68835-8',
  display: 'Primary care Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrimaryCareProcedureNote: Coding = {
  code: '68836-6',
  display: 'Primary care procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrimaryCareHospitalConsultNote: Coding = {
  code: '68837-4',
  display: 'Primary care Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrimaryCareHospitalLetter: Coding = {
  code: '68838-2',
  display: 'Primary care Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ResearchNote: Coding = {
  code: '68839-0',
  display: 'Research Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ResearchProgressNote: Coding = {
  code: '68840-8',
  display: 'Research Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechLanguagePathologyDischargeSummary: Coding = {
  code: '68841-6',
  display: 'Speech-language pathology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechLanguagePathologyHistoryAndPhysicalNote: Coding = {
  code: '68843-2',
  display: 'Speech-language pathology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechLanguagePathologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68844-0',
  display: 'Speech-language pathology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechLanguagePathologyHospitalConsultNote: Coding = {
  code: '68846-5',
  display: 'Speech-language pathology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpeechLanguagePathologyHospitalLetter: Coding = {
  code: '68847-3',
  display: 'Speech-language pathology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TransplantSurgeryNote: Coding = {
  code: '68848-1',
  display: 'Transplant surgery Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TransplantSurgeryHistoryAndPhysicalNote: Coding = {
  code: '68849-9',
  display: 'Transplant surgery History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TransplantSurgeryPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68850-7',
  display: 'Transplant surgery Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TransplantSurgeryProcedureNote: Coding = {
  code: '68851-5',
  display: 'Transplant surgery procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TransplantSurgeryHospitalConsultNote: Coding = {
  code: '68852-3',
  display: 'Transplant surgery Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TransplantSurgeryHospitalLetter: Coding = {
  code: '68853-1',
  display: 'Transplant surgery Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricRehabilitationMedicineNote: Coding = {
  code: '68854-9',
  display: 'Pediatric rehabilitation medicine Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricTransplantHepatologyDiagnosticStudyNote: Coding = {
  code: '68855-6',
  display: 'Pediatric transplant hepatology Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricTransplantHepatologyDischargeSummary: Coding = {
  code: '68856-4',
  display: 'Pediatric transplant hepatology Discharge summary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricTransplantHepatologyHistoryAndPhysicalNote: Coding = {
  code: '68858-0',
  display: 'Pediatric transplant hepatology History and physical note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricTransplantHepatologyNote: Coding = {
  code: '68859-8',
  display: 'Pediatric transplant hepatology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricTransplantHepatologyPreoperativeEvaluationAndManagementNote: Coding = {
  code: '68860-6',
  display: 'Pediatric transplant hepatology Preoperative evaluation and management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricTransplantHepatologyProcedureNote: Coding = {
  code: '68861-4',
  display: 'Pediatric transplant hepatology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricTransplantHepatologyProgressNote: Coding = {
  code: '68862-2',
  display: 'Pediatric transplant hepatology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricTransplantHepatologyTransferSummaryNote: Coding = {
  code: '68863-0',
  display: 'Pediatric transplant hepatology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricTransplantHepatologyHospitalConsultNote: Coding = {
  code: '68864-8',
  display: 'Pediatric transplant hepatology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricTransplantHepatologyHospitalLetter: Coding = {
  code: '68865-5',
  display: 'Pediatric transplant hepatology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricNephrologyLetter: Coding = {
  code: '68866-3',
  display: 'Pediatric nephrology Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricNephrologyNote: Coding = {
  code: '68867-1',
  display: 'Pediatric nephrology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricNephrologyProcedureNote: Coding = {
  code: '68868-9',
  display: 'Pediatric nephrology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricNephrologyHospitalConsultNote: Coding = {
  code: '68869-7',
  display: 'Pediatric nephrology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricNephrologyHospitalLetter: Coding = {
  code: '68870-5',
  display: 'Pediatric nephrology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricOtolaryngologyNote: Coding = {
  code: '68871-3',
  display: 'Pediatric otolaryngology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricOtolaryngologyProcedureNote: Coding = {
  code: '68872-1',
  display: 'Pediatric otolaryngology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricOtolaryngologyProgressNote: Coding = {
  code: '68873-9',
  display: 'Pediatric otolaryngology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricOtolaryngologyHospitalConsultNote: Coding = {
  code: '68874-7',
  display: 'Pediatric otolaryngology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricOtolaryngologyHospitalLetter: Coding = {
  code: '68875-4',
  display: 'Pediatric otolaryngology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricRheumatologyNote: Coding = {
  code: '68876-2',
  display: 'Pediatric rheumatology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricRheumatologyProcedureNote: Coding = {
  code: '68877-0',
  display: 'Pediatric rheumatology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricRheumatologyProgressNote: Coding = {
  code: '68878-8',
  display: 'Pediatric rheumatology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricRheumatologyHospitalConsultNote: Coding = {
  code: '68879-6',
  display: 'Pediatric rheumatology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricRheumatologyHospitalLetter: Coding = {
  code: '68880-4',
  display: 'Pediatric rheumatology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricSurgeryNote: Coding = {
  code: '68881-2',
  display: 'Pediatric surgery Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricUrologyNote: Coding = {
  code: '68882-0',
  display: 'Pediatric urology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsTransferSummaryNote: Coding = {
  code: '68883-8',
  display: 'Pediatrics Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricsHospitalTransferSummaryNote: Coding = {
  code: '68884-6',
  display: 'Pediatrics Hospital Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OphthalmologyTransferSummaryNote: Coding = {
  code: '68887-9',
  display: 'Ophthalmology Transfer summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricDermatologyNote: Coding = {
  code: '68889-5',
  display: 'Pediatric dermatology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricDermatologyProcedureNote: Coding = {
  code: '68890-3',
  display: 'Pediatric dermatology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricDermatologyProgressNote: Coding = {
  code: '68891-1',
  display: 'Pediatric dermatology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricDermatologyHospitalConsultNote: Coding = {
  code: '68892-9',
  display: 'Pediatric dermatology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricDermatologyHospitalLetter: Coding = {
  code: '68893-7',
  display: 'Pediatric dermatology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricEndocrinologyNote: Coding = {
  code: '68894-5',
  display: 'Pediatric endocrinology Note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricEndocrinologyProcedureNote: Coding = {
  code: '68895-2',
  display: 'Pediatric endocrinology procedure note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricEndocrinologyProgressNote: Coding = {
  code: '68896-0',
  display: 'Pediatric endocrinology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricEndocrinologyHospitalConsultNote: Coding = {
  code: '68897-8',
  display: 'Pediatric endocrinology Hospital Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PediatricEndocrinologyHospitalLetter: Coding = {
  code: '68898-6',
  display: 'Pediatric endocrinology Hospital Letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AorticArchFluoroscopicAngiogramWContrastIA: Coding = {
  code: '69054-5',
  display: 'Aortic arch Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AcromioclavicularJointBilateralXRayWOWeight: Coding = {
  code: '69055-2',
  display: 'Acromioclavicular joint - bilateral X-ray WO weight',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralXRayAndObliques: Coding = {
  code: '69056-0',
  display: 'Elbow - bilateral X-ray and obliques',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralXRayAPAndLateralAndOblique: Coding = {
  code: '69057-8',
  display: 'Hand - bilateral X-ray AP and lateral and oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralXRay2Views: Coding = {
  code: '69058-6',
  display: 'Hip - bilateral X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipBilateralXRayAndLateralCrosstable: Coding = {
  code: '69059-4',
  display: 'Hip - bilateral X-ray and lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRay2ViewsAndSunrise: Coding = {
  code: '69060-2',
  display: 'Knee - bilateral X-ray 2 views and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRay2ViewsAndTunnel: Coding = {
  code: '69061-0',
  display: 'Knee - bilateral X-ray 2 views and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRay4ViewsStanding: Coding = {
  code: '69062-8',
  display: 'Knee - bilateral X-ray 4 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRay4ViewsAndSunriseAndTunnel: Coding = {
  code: '69063-6',
  display: 'Knee - bilateral X-ray 4 views and Sunrise and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralXRaySunriseAndViewsStanding: Coding = {
  code: '69064-4',
  display: 'Knee - bilateral X-ray Sunrise and (views standing)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayAPAndLateralCrosstable: Coding = {
  code: '69065-1',
  display: 'Abdomen X-ray AP and lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalVesselsFluoroscopicAngiogramWContrastIV: Coding = {
  code: '69066-9',
  display: 'Abdominal vessels Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionFluoroscopicAngiogramAngioplastyWContrast: Coding = {
  code: '69067-7',
  display: 'Unspecified body region Fluoroscopic angiogram Angioplasty W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceForNeedleLocalizationOfBreastBilateral: Coding = {
  code: '69068-5',
  display: 'Mammogram Guidance for needle localization of Breast - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaBilateralXRaySunrise: Coding = {
  code: '69069-3',
  display: 'Patella - bilateral X-ray Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsBilateralXRayAnteriorAndLateral: Coding = {
  code: '69070-1',
  display: 'Ribs - bilateral X-ray anterior and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RibsBilateralAndChestXRay: Coding = {
  code: '69071-9',
  display: 'Ribs - bilateral and Chest X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralXRayUlnarDeviationAndRadialDeviation: Coding = {
  code: '69072-7',
  display: 'Wrist - bilateral X-ray ulnar deviation and radial deviation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForCoreNeedleBiopsyOfUnspecifiedBodyRegion: Coding = {
  code: '69073-5',
  display: 'Fluoroscopy Guidance for core needle biopsy of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfPelvis: Coding = {
  code: '69074-3',
  display: 'Fluoroscopy Guidance for biopsy of Pelvis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfSalivaryGland: Coding = {
  code: '69075-0',
  display: 'Fluoroscopy Guidance for biopsy of Salivary gland',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfBone: Coding = {
  code: '69076-8',
  display: 'Fluoroscopy Guidance for biopsy of Bone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BrachiocephalicArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '69077-6',
  display: 'Brachiocephalic artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForDrainageOfChest: Coding = {
  code: '69078-4',
  display: 'Fluoroscopy Guidance for drainage of Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClavicleXRay45DegreeCephalicAngle: Coding = {
  code: '69079-2',
  display: 'Clavicle X-ray 45 degree cephalic angle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRay5ViewsWFlexionAndWExtension: Coding = {
  code: '69080-0',
  display: 'Spine Cervical X-ray 5 views W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalXRay5ViewsAndSwimmers: Coding = {
  code: '69081-8',
  display: 'Spine Cervical X-ray 5 views and Swimmers',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeadCTAnd3DReconstructionWOContrast: Coding = {
  code: '69082-6',
  display: 'Head CT and 3D reconstruction WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfAbdomenWOContrast: Coding = {
  code: '69083-4',
  display: 'CT Guidance for biopsy of Abdomen-- WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestVesselsCTAngiogramWOContrast: Coding = {
  code: '69084-2',
  display: 'Chest vessels CT angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsCTAngiogramWAndWOContrast: Coding = {
  code: '69085-9',
  display: 'Renal vessels CT angiogram W and WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaCTWAndWOContrast: Coding = {
  code: '69086-7',
  display: 'Aorta CT W and WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralCTWOContrast: Coding = {
  code: '69087-5',
  display: 'Ankle - bilateral CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralCTWContrastIV: Coding = {
  code: '69088-3',
  display: 'Knee - bilateral CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeBilateralCTWOContrast: Coding = {
  code: '69089-1',
  display: 'Knee - bilateral CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralCTWOContrast: Coding = {
  code: '69090-9',
  display: 'Shoulder - bilateral CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristBilateralCTWContrastIV: Coding = {
  code: '69091-7',
  display: 'Wrist - bilateral CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfLiverWOContrast: Coding = {
  code: '69092-5',
  display: 'CT Guidance for biopsy of Liver-- WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfPelvisWContrastIV: Coding = {
  code: '69093-3',
  display: 'CT Guidance for biopsy of Pelvis-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForBiopsyOfPelvisWOContrast: Coding = {
  code: '69094-1',
  display: 'CT Guidance for biopsy of Pelvis-- WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BladderCTWContrastIV: Coding = {
  code: '69095-8',
  display: 'Bladder CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestCTLimitedWContrastIV: Coding = {
  code: '69096-6',
  display: 'Chest CT limited W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfLiver: Coding = {
  code: '69097-4',
  display: 'CT Guidance for needle biopsy of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfMuscle: Coding = {
  code: '69098-2',
  display: 'CT Guidance for needle biopsy of Muscle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfChestPleura: Coding = {
  code: '69099-0',
  display: 'CT Guidance for needle biopsy of Chest.pleura',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfSalivaryGland: Coding = {
  code: '69100-6',
  display: 'CT Guidance for needle biopsy of Salivary gland',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNeedleBiopsyOfThyroid: Coding = {
  code: '69101-4',
  display: 'CT Guidance for needle biopsy of Thyroid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftCTWContrastIntraarticular: Coding = {
  code: '69102-2',
  display: 'Ankle - left CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftCTWContrastIntraarticular: Coding = {
  code: '69103-0',
  display: 'Elbow - left CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityLeftCTWOContrast: Coding = {
  code: '69104-8',
  display: 'Extremity - left CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftCTWContrastIntraarticular: Coding = {
  code: '69105-5',
  display: 'Hip - left CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftCTWContrastIntraarticular: Coding = {
  code: '69106-3',
  display: 'Knee - left CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftCTWContrastIntraarticular: Coding = {
  code: '69107-1',
  display: 'Wrist - left CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryVesselsCTAngiogramWAndWOContrast: Coding = {
  code: '69108-9',
  display: 'Pulmonary vessels CT angiogram W and WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightCTWContrastIntraarticular: Coding = {
  code: '69109-7',
  display: 'Ankle - right CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightCTWContrastIntraarticular: Coding = {
  code: '69110-5',
  display: 'Elbow - right CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityRightCTWOContrast: Coding = {
  code: '69111-3',
  display: 'Extremity - right CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightCTWContrastIntraarticular: Coding = {
  code: '69112-1',
  display: 'Hip - right CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyRightCT: Coding = {
  code: '69113-9',
  display: 'Kidney - right CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightCTWContrastIntraarticular: Coding = {
  code: '69114-7',
  display: 'Knee - right CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightCTWContrastIntraarticular: Coding = {
  code: '69115-4',
  display: 'Wrist - right CT W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumAndCoccyxCT: Coding = {
  code: '69116-2',
  display: 'Sacrum and Coccyx CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaCT: Coding = {
  code: '69117-0',
  display: 'Scapula CT',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScapulaCTWOContrast: Coding = {
  code: '69118-8',
  display: 'Scapula CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaThoracicCTAngiogramWOContrast: Coding = {
  code: '69119-6',
  display: 'Aorta thoracic CT angiogram WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForAbscessDrainageOfNeck: Coding = {
  code: '69120-4',
  display: 'Fluoroscopy Guidance for abscess drainage of Neck',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForAspirationOfCystOfOvary: Coding = {
  code: '69121-2',
  display: 'Fluoroscopy Guidance for aspiration of cyst of Ovary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForAbscessDrainageOfPancreas: Coding = {
  code: '69122-0',
  display: 'Fluoroscopy Guidance for abscess drainage of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForAbscessDrainageOfPleuralSpace: Coding = {
  code: '69123-8',
  display: 'Fluoroscopy Guidance for abscess drainage of Pleural space',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfSuperficialTissue: Coding = {
  code: '69124-6',
  display: 'Fluoroscopy Guidance for fine needle aspiration of Superficial tissue',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfLiver: Coding = {
  code: '69125-3',
  display: 'Fluoroscopy Guidance for needle biopsy of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfPancreas: Coding = {
  code: '69126-1',
  display: 'Fluoroscopy Guidance for needle biopsy of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfChestPleura: Coding = {
  code: '69127-9',
  display: 'Fluoroscopy Guidance for needle biopsy of Chest.pleura',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfSalivaryGland: Coding = {
  code: '69128-7',
  display: 'Fluoroscopy Guidance for needle biopsy of Salivary gland',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfThyroid: Coding = {
  code: '69129-5',
  display: 'Fluoroscopy Guidance for needle biopsy of Thyroid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandXRayAPAndLateral: Coding = {
  code: '69130-3',
  display: 'Hand X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayAndDaneliusMiller: Coding = {
  code: '69131-1',
  display: 'Hip X-ray and Danelius Miller',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipXRayDaneliusMiller: Coding = {
  code: '69132-9',
  display: 'Hip X-ray Danelius Miller',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForDrainageOfHip: Coding = {
  code: '69133-7',
  display: 'Fluoroscopy Guidance for drainage of Hip',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInIliacArtery: Coding = {
  code: '69134-5',
  display: 'Fluoroscopic angiogram Guidance for placement of stent in Iliac artery',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IliacArteryFluoroscopicAngiogramAtherectomyWContrast: Coding = {
  code: '69135-2',
  display: 'Iliac artery Fluoroscopic angiogram Atherectomy W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeXRaySunriseAndTunnel: Coding = {
  code: '69136-0',
  display: 'Knee X-ray Sunrise and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRayAPAndLateralAndObliqueStanding: Coding = {
  code: '69137-8',
  display: 'Ankle - left X-ray AP and lateral and oblique standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRay3ViewsStanding: Coding = {
  code: '69138-6',
  display: 'Ankle - left X-ray 3 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayAndLateralCrosstable: Coding = {
  code: '69139-4',
  display: 'Hip - left X-ray and lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayAndDaneliusMiller: Coding = {
  code: '69140-2',
  display: 'Hip - left X-ray and Danelius Miller',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftXRayDaneliusMiller: Coding = {
  code: '69141-0',
  display: 'Hip - left X-ray Danelius Miller',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay2ViewsAndSunrise: Coding = {
  code: '69142-8',
  display: 'Knee - left X-ray 2 views and Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay2ViewsAndTunnelStanding: Coding = {
  code: '69143-6',
  display: 'Knee - left X-ray 2 views and tunnel standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay4ViewsAndAPStanding: Coding = {
  code: '69144-4',
  display: 'Knee - left X-ray 4 views and AP standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRay4ViewsAndTunnel: Coding = {
  code: '69145-1',
  display: 'Knee - left X-ray 4 views and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayAPAndLateralCrosstable: Coding = {
  code: '69146-9',
  display: 'Knee - left X-ray AP and lateral crosstable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayAPAndLateralAndRightObliqueAndLeftOblique: Coding = {
  code: '69147-7',
  display: 'Knee - left X-ray AP and lateral and right oblique and left oblique',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRayAndTunnel: Coding = {
  code: '69148-5',
  display: 'Knee - left X-ray and tunnel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeLeftXRaySunriseAndViewsStanding: Coding = {
  code: '69149-3',
  display: 'Knee - left X-ray Sunrise and (views standing)',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantLeftMammogramDiagnostic: Coding = {
  code: '69150-1',
  display: 'Breast implant - left Mammogram diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRay3ViewsScaphoid: Coding = {
  code: '69151-9',
  display: 'Wrist - left X-ray 3 views scaphoid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaLeftXRaySingleView: Coding = {
  code: '69152-7',
  display: 'Patella - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayAPAndGrasheyAndAxillary: Coding = {
  code: '69153-5',
  display: 'Shoulder - left X-ray AP and Grashey and axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRay3ViewsAndAxillary: Coding = {
  code: '69154-3',
  display: 'Shoulder - left X-ray 3 views and axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRay3ViewsAndY: Coding = {
  code: '69155-0',
  display: 'Shoulder - left X-ray 3 views and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftXRayGrasheyAndY: Coding = {
  code: '69156-8',
  display: 'Shoulder - left X-ray Grashey and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftXRayLateralWFlexionAndWExtension: Coding = {
  code: '69157-6',
  display: 'Wrist - left X-ray lateral W flexion and W extension',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantXRayDiagnostic: Coding = {
  code: '69158-4',
  display: 'Breast implant X-ray diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantXRayScreening: Coding = {
  code: '69159-2',
  display: 'Breast implant X-ray screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MammogramGuidanceStereotacticForNeedleBiopsyOfBreast: Coding = {
  code: '69160-0',
  display: 'Mammogram Guidance.stereotactic for needle biopsy of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CircleOfWillisMRIAngiogramWAndWOContrastIV: Coding = {
  code: '69161-8',
  display: 'Circle of Willis MRI angiogram W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryArteryBilateralMRIAngiogramWContrastIA: Coding = {
  code: '69162-6',
  display: 'Pulmonary artery - bilateral MRI angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralMRIWContrastIV: Coding = {
  code: '69163-4',
  display: 'Ankle - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleBilateralMRIWOContrast: Coding = {
  code: '69164-2',
  display: 'Ankle - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantBilateralMRI: Coding = {
  code: '69165-9',
  display: 'Breast implant - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantBilateralMRIWAndWOContrastIV: Coding = {
  code: '69166-7',
  display: 'Breast implant - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantBilateralMRIWContrastIV: Coding = {
  code: '69167-5',
  display: 'Breast implant - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantBilateralMRIWOContrast: Coding = {
  code: '69168-3',
  display: 'Breast implant - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceForBiopsyOfBreastBilateral: Coding = {
  code: '69169-1',
  display: 'MRI Guidance for biopsy of Breast - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralMRIWContrastIV: Coding = {
  code: '69170-9',
  display: 'Elbow - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowBilateralMRIWOContrast: Coding = {
  code: '69171-7',
  display: 'Elbow - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurBilateralMRIWContrastIV: Coding = {
  code: '69172-5',
  display: 'Femur - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemurBilateralMRIWOContrast: Coding = {
  code: '69173-3',
  display: 'Femur - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmBilateralMRIWAndWOContrastIV: Coding = {
  code: '69174-1',
  display: 'Forearm - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmBilateralMRIWContrastIV: Coding = {
  code: '69175-8',
  display: 'Forearm - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ForearmBilateralMRIWOContrast: Coding = {
  code: '69176-6',
  display: 'Forearm - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralMRIWAndWOContrastIV: Coding = {
  code: '69177-4',
  display: 'Hand - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralMRIWContrastIV: Coding = {
  code: '69178-2',
  display: 'Hand - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandBilateralMRIWOContrast: Coding = {
  code: '69179-0',
  display: 'Hand - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmBilateralMRI: Coding = {
  code: '69180-8',
  display: 'Upper arm - bilateral MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmBilateralMRIWAndWOContrastIV: Coding = {
  code: '69181-6',
  display: 'Upper arm - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmBilateralMRIWContrastIV: Coding = {
  code: '69182-4',
  display: 'Upper arm - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperArmBilateralMRIWOContrast: Coding = {
  code: '69183-2',
  display: 'Upper arm - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderBilateralMRIWContrastIV: Coding = {
  code: '69184-0',
  display: 'Shoulder - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerLegBilateralMRIWOContrast: Coding = {
  code: '69185-7',
  display: 'Lower leg - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityBilateralMRIWAndWOContrastIV: Coding = {
  code: '69186-5',
  display: 'Upper extremity - bilateral MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityBilateralMRIWContrastIV: Coding = {
  code: '69187-3',
  display: 'Upper extremity - bilateral MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityBilateralMRIWOContrast: Coding = {
  code: '69188-1',
  display: 'Upper extremity - bilateral MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantMRIWAndWOContrastIV: Coding = {
  code: '69189-9',
  display: 'Breast implant MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantMRIWContrastIV: Coding = {
  code: '69190-7',
  display: 'Breast implant MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantMRIWOContrast: Coding = {
  code: '69191-5',
  display: 'Breast implant MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceForAspirationOfCystOfBreast: Coding = {
  code: '69192-3',
  display: 'MRI Guidance for aspiration of cyst of Breast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityMRI: Coding = {
  code: '69193-1',
  display: 'Extremity MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerMRIWAndWOContrastIV: Coding = {
  code: '69194-9',
  display: 'Finger MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerMRIWContrastIV: Coding = {
  code: '69195-6',
  display: 'Finger MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerMRIWOContrast: Coding = {
  code: '69196-4',
  display: 'Finger MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceForNeedleBiopsyOfLiver: Coding = {
  code: '69197-2',
  display: 'MRI Guidance for needle biopsy of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceForNeedleBiopsyOfMuscle: Coding = {
  code: '69198-0',
  display: 'MRI Guidance for needle biopsy of Muscle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceForNeedleBiopsyOfPancreas: Coding = {
  code: '69199-8',
  display: 'MRI Guidance for needle biopsy of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceForNeedleBiopsyOfPleura: Coding = {
  code: '69200-4',
  display: 'MRI Guidance for needle biopsy of Pleura',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceForNeedleBiopsyOfSalivaryGland: Coding = {
  code: '69201-2',
  display: 'MRI Guidance for needle biopsy of Salivary gland',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceForNeedleBiopsyOfThyroid: Coding = {
  code: '69202-0',
  display: 'MRI Guidance for needle biopsy of Thyroid',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceForBiopsyOfBreastLeft: Coding = {
  code: '69203-8',
  display: 'MRI Guidance for biopsy of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerLeftMRIWAndWOContrastIV: Coding = {
  code: '69204-6',
  display: 'Finger - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerLeftMRIWContrastIV: Coding = {
  code: '69205-3',
  display: 'Finger - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerLeftMRIWOContrast: Coding = {
  code: '69206-1',
  display: 'Finger - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipLeftMRIWAndWOContrastIntraarticular: Coding = {
  code: '69207-9',
  display: 'Hip - left MRI W and WO contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderLeftMRIWAndWOContrastIntraarticular: Coding = {
  code: '69208-7',
  display: 'Shoulder - left MRI W and WO contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristLeftAndHandLeftMRI: Coding = {
  code: '69209-5',
  display: 'Wrist - left and Hand - left MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityJointMRIWContrastIntraarticular: Coding = {
  code: '69210-3',
  display: 'Lower Extremity Joint MRI W contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_NasalBonesMRI: Coding = {
  code: '69211-1',
  display: 'Nasal bones MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisMRILimited: Coding = {
  code: '69212-9',
  display: 'Pelvis MRI limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MRIGuidanceForBiopsyOfBreastRight: Coding = {
  code: '69213-7',
  display: 'MRI Guidance for biopsy of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerRightMRIWAndWOContrastIV: Coding = {
  code: '69214-5',
  display: 'Finger - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerRightMRIWContrastIV: Coding = {
  code: '69215-2',
  display: 'Finger - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FingerRightMRIWOContrast: Coding = {
  code: '69216-0',
  display: 'Finger - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HipRightMRIWAndWOContrastIntraarticular: Coding = {
  code: '69217-8',
  display: 'Hip - right MRI W and WO contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightMRIWAndWOContrastIntraarticular: Coding = {
  code: '69218-6',
  display: 'Shoulder - right MRI W and WO contrast intraarticular',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightAndHandRightMRI: Coding = {
  code: '69219-4',
  display: 'Wrist - right and Hand - right MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullBaseMRIWAndWOContrastIV: Coding = {
  code: '69220-2',
  display: 'Skull.base MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleMRIWContrastIV: Coding = {
  code: '69221-0',
  display: 'Scrotum and Testicle MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VenaCavaMRI: Coding = {
  code: '69222-8',
  display: 'Vena cava MRI',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionMRIWOContrast: Coding = {
  code: '69223-6',
  display: 'Unspecified body region MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfAbdomen: Coding = {
  code: '69224-4',
  display: 'Fluoroscopy Guidance for needle biopsy of Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfChest: Coding = {
  code: '69225-1',
  display: 'Fluoroscopy Guidance for needle biopsy of Chest',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfMuscle: Coding = {
  code: '69226-9',
  display: 'Fluoroscopy Guidance for needle biopsy of Muscle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfPleura: Coding = {
  code: '69227-7',
  display: 'Fluoroscopy Guidance for needle biopsy of Pleura',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfProstate: Coding = {
  code: '69228-5',
  display: 'Fluoroscopy Guidance for needle biopsy of Prostate',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverSPECTWTc99mSCIV: Coding = {
  code: '69229-3',
  display: 'Liver SPECT W Tc-99m SC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LiverScanWTc99mSCIV: Coding = {
  code: '69230-1',
  display: 'Liver Scan W Tc-99m SC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanWStressAndWTc99mIV: Coding = {
  code: '69231-9',
  display: 'Heart Scan W stress and W Tc-99m IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartScanWStressAndWTc99mSestamibiIV: Coding = {
  code: '69232-7',
  display: 'Heart Scan W stress and W Tc-99m Sestamibi IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ParotidGlandScanWTc99mPertechnetateIV: Coding = {
  code: '69233-5',
  display: 'Parotid gland Scan W Tc-99m pertechnetate IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpleenSPECTWTc99mTaggedRBCIV: Coding = {
  code: '69234-3',
  display: 'Spleen SPECT W Tc-99m tagged RBC IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleSPECTFlow: Coding = {
  code: '69235-0',
  display: 'Scrotum and Testicle SPECT flow',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ThyroidScanAndUptakeWI131PO: Coding = {
  code: '69236-8',
  display: 'Thyroid Scan and uptake W I-131 PO',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SPECTForTumorWholeBody: Coding = {
  code: '69237-6',
  display: 'SPECT for tumor whole body',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrinaryBladderAndUrethraSPECTWContrastIntraBladderDuringVoiding: Coding = {
  code: '69238-4',
  display: 'Urinary Bladder and Urethra SPECT W contrast intra bladder during voiding',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaXRaySunrise: Coding = {
  code: '69239-2',
  display: 'Patella X-ray Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousBiopsyOfAbdomen: Coding = {
  code: '69240-0',
  display: 'Fluoroscopy Guidance for percutaneous biopsy of Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfAbdomen: Coding = {
  code: '69241-8',
  display: 'Fluoroscopy Guidance for percutaneous drainage of abscess of Abdomen',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfAppendix: Coding = {
  code: '69242-6',
  display: 'Fluoroscopy Guidance for percutaneous drainage of abscess of Appendix',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfLung: Coding = {
  code: '69243-4',
  display: 'Fluoroscopy Guidance for percutaneous drainage of abscess of Lung',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfPelvis: Coding = {
  code: '69244-2',
  display: 'Fluoroscopy Guidance for percutaneous drainage of abscess of Pelvis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousNeedleBiopsyOfKidney: Coding = {
  code: '69245-9',
  display: 'Fluoroscopy Guidance for percutaneous needle biopsy of Kidney',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousNeedleBiopsyOfLiver: Coding = {
  code: '69246-7',
  display: 'Fluoroscopy Guidance for percutaneous needle biopsy of Liver',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousNeedleBiopsyOfSalivaryGland: Coding = {
  code: '69247-5',
  display: 'Fluoroscopy Guidance for percutaneous needle biopsy of Salivary gland',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalArteryFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIA: Coding = {
  code: '69248-3',
  display: 'Renal artery Fluoroscopic angiogram Percutaneous transluminal angioplasty of vessel W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PoplitealArteryFluoroscopicAngiogramWContrastIA: Coding = {
  code: '69249-1',
  display: 'Popliteal artery Fluoroscopic angiogram W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PortalVeinFluoroscopicAngiogramWContrastIV: Coding = {
  code: '69250-9',
  display: 'Portal vein Fluoroscopic angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastMammogramPostWirePlacement: Coding = {
  code: '69251-7',
  display: 'Breast Mammogram Post Wire Placement',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PulmonaryArteryFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIA: Coding = {
  code: '69252-5',
  display: 'Pulmonary artery Fluoroscopic angiogram Percutaneous transluminal angioplasty of vessel W contrast IA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsFluoroscopicAngiogramAtherectomyWContrast: Coding = {
  code: '69253-3',
  display: 'Renal vessels Fluoroscopic angiogram Atherectomy W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRay3ViewsStanding: Coding = {
  code: '69254-1',
  display: 'Ankle - right X-ray 3 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRaySunriseAndTunnelStanding: Coding = {
  code: '69255-8',
  display: 'Knee - right X-ray Sunrise and tunnel standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KneeRightXRaySunrise: Coding = {
  code: '69256-6',
  display: 'Knee - right X-ray Sunrise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightXRay2Views: Coding = {
  code: '69257-4',
  display: 'Lower extremity - right X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityRightXRayAPAndLateral: Coding = {
  code: '69258-2',
  display: 'Lower extremity - right X-ray AP and lateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastImplantRightMammogramDiagnostic: Coding = {
  code: '69259-0',
  display: 'Breast implant - right Mammogram diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaRightXRaySingleView: Coding = {
  code: '69260-8',
  display: 'Patella - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PatellaRightXRay3Views: Coding = {
  code: '69261-6',
  display: 'Patella - right X-ray 3 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderRightXRayAPAndGrasheyAndAxillary: Coding = {
  code: '69262-4',
  display: 'Shoulder - right X-ray AP and Grashey and axillary',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristRightXRayPAWClenchedFist: Coding = {
  code: '69263-2',
  display: 'Wrist - right X-ray PA W clenched fist',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SacrumXRayStanding: Coding = {
  code: '69264-0',
  display: 'Sacrum X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRay4Views: Coding = {
  code: '69265-7',
  display: 'Shoulder X-ray 4 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayAPAndY: Coding = {
  code: '69266-5',
  display: 'Shoulder X-ray AP and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ShoulderXRayGrasheyAndAxillaryAndY: Coding = {
  code: '69267-3',
  display: 'Shoulder X-ray Grashey and axillary and Y',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastDuctMammogramSingleViewWContrastIntraDuct: Coding = {
  code: '69268-1',
  display: 'Breast duct Mammogram Single view W contrast intra duct',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayAPSingleView: Coding = {
  code: '69269-9',
  display: 'Skull X-ray AP single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayPA: Coding = {
  code: '69270-7',
  display: 'Skull X-ray PA',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SkullXRayPAAndLateralAndWatersAndTowne: Coding = {
  code: '69271-5',
  display: 'Skull X-ray PA and lateral and Waters and Towne',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SmallBowelFluoroscopyWContrastViaIleostomy: Coding = {
  code: '69272-3',
  display: 'Small bowel Fluoroscopy W contrast via ileostomy',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracolumbarJunctionXRay2Views: Coding = {
  code: '69273-1',
  display: 'Spine Thoracolumbar Junction X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRay2ViewsStanding: Coding = {
  code: '69274-9',
  display: 'Spine Thoracic X-ray 2 views standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRayStanding: Coding = {
  code: '69275-6',
  display: 'Spine Thoracic X-ray standing',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAbdominalUS: Coding = {
  code: '69276-4',
  display: 'Aorta abdominal US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AdrenalGlandUS: Coding = {
  code: '69277-2',
  display: 'Adrenal gland US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfBreastBilateral: Coding = {
  code: '69278-0',
  display: 'US Guidance for aspiration of Breast - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForCoreNeedleBiopsyOfLymphNode: Coding = {
  code: '69279-8',
  display: 'US Guidance for core needle biopsy of Lymph node',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BladderUSLimited: Coding = {
  code: '69280-6',
  display: 'Bladder US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestUSLimited: Coding = {
  code: '69281-4',
  display: 'Chest US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UnspecifiedBodyRegionUSDopplerLimited: Coding = {
  code: '69282-2',
  display: 'Unspecified body region US.doppler limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVeinsBilateralUSDoppler: Coding = {
  code: '69283-0',
  display: 'Extremity veins - bilateral US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PortalVeinAndHepaticVeinUSDoppler: Coding = {
  code: '69284-8',
  display: 'Portal vein and Hepatic vein US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UmbilicalArteryUSDoppler: Coding = {
  code: '69285-5',
  display: 'Umbilical artery US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EyeUSLimited: Coding = {
  code: '69286-3',
  display: 'Eye US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfLymphNode: Coding = {
  code: '69287-1',
  display: 'US Guidance for aspiration of Lymph node',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfMuscle: Coding = {
  code: '69288-9',
  display: 'US Guidance for needle biopsy of Muscle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfPancreas: Coding = {
  code: '69289-7',
  display: 'US Guidance for needle biopsy of Pancreas',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfBreastRight: Coding = {
  code: '69290-5',
  display: 'US Guidance for needle biopsy of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfSalivaryGland: Coding = {
  code: '69291-3',
  display: 'US Guidance for needle biopsy of Salivary gland',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfBreastLeft: Coding = {
  code: '69292-1',
  display: 'US Guidance for aspiration of Breast - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteryLeftUS: Coding = {
  code: '69293-9',
  display: 'Extremity artery - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalArteryUS: Coding = {
  code: '69294-7',
  display: 'Renal artery US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RenalVesselsUS: Coding = {
  code: '69295-4',
  display: 'Renal vessels US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAspirationOfBreastRight: Coding = {
  code: '69296-2',
  display: 'US Guidance for aspiration of Breast - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteryRightUS: Coding = {
  code: '69297-0',
  display: 'Extremity artery - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SalivaryGlandUS: Coding = {
  code: '69298-8',
  display: 'Salivary gland US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ScrotumAndTesticleUSLimited: Coding = {
  code: '69299-6',
  display: 'Scrotum and Testicle US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyTransplantUSLimited: Coding = {
  code: '69300-2',
  display: 'Kidney transplant US limited',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIV: Coding = {
  code: '69301-0',
  display: 'Upper extremity vein Fluoroscopic angiogram Percutaneous transluminal angioplasty of vessel W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRayWClenchedFist: Coding = {
  code: '69302-8',
  display: 'Wrist X-ray W clenched fist',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRayUlnarDeviationAndRadialDeviation: Coding = {
  code: '69303-6',
  display: 'Wrist X-ray ulnar deviation and radial deviation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_WristXRayUlnarDeviation: Coding = {
  code: '69304-4',
  display: 'Wrist X-ray ulnar deviation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ZygomaticArchXRay2Views: Coding = {
  code: '69305-1',
  display: 'Zygomatic arch X-ray 2 views',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForAspirationOfCystOfBone: Coding = {
  code: '69306-9',
  display: 'Fluoroscopy Guidance for aspiration of cyst of Bone',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleLeftXRaySingleView: Coding = {
  code: '69307-7',
  display: 'Ankle - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowLeftXRaySingleView: Coding = {
  code: '69308-5',
  display: 'Elbow - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootLeftXRaySingleView: Coding = {
  code: '69309-3',
  display: 'Foot - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandLeftXRaySingleView: Coding = {
  code: '69310-1',
  display: 'Hand - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusLeftXRaySingleView: Coding = {
  code: '69311-9',
  display: 'Calcaneus - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusLeftXRaySingleView: Coding = {
  code: '69312-7',
  display: 'Humerus - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaLeftAndFibulaLeftXRaySingleView: Coding = {
  code: '69313-5',
  display: 'Tibia - left and Fibula - left X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnkleRightXRaySingleView: Coding = {
  code: '69314-3',
  display: 'Ankle - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ElbowRightXRaySingleView: Coding = {
  code: '69315-0',
  display: 'Elbow - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FootRightXRaySingleView: Coding = {
  code: '69316-8',
  display: 'Foot - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiusRightAndUlnaRightXRaySingleView: Coding = {
  code: '69317-6',
  display: 'Radius - right and Ulna - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HandRightXRaySingleView: Coding = {
  code: '69318-4',
  display: 'Hand - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CalcaneusRightXRaySingleView: Coding = {
  code: '69319-2',
  display: 'Calcaneus - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HumerusRightXRaySingleView: Coding = {
  code: '69320-0',
  display: 'Humerus - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TibiaRightAndFibulaRightXRaySingleView: Coding = {
  code: '69321-8',
  display: 'Tibia - right and Fibula - right X-ray Single view',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinsBilateralUS: Coding = {
  code: '69385-3',
  display: 'Lower extremity veins - bilateral US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfEpididymis: Coding = {
  code: '69387-9',
  display: 'US Guidance for biopsy of Epididymis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UrinaryBladderUSPostVoid: Coding = {
  code: '69388-7',
  display: 'Urinary bladder US post void',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralArteryAndPoplitealArteryUS: Coding = {
  code: '69389-5',
  display: 'Femoral artery and Popliteal artery US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OvaryUS: Coding = {
  code: '69390-3',
  display: 'Ovary US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForCordocentesis: Coding = {
  code: '69391-1',
  display: 'US Guidance for cordocentesis',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LowerExtremityVeinsLeftUS: Coding = {
  code: '69392-9',
  display: 'Lower extremity veins - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarUS: Coding = {
  code: '69393-7',
  display: 'Spine Lumbar US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MesentericArteryUS: Coding = {
  code: '69394-5',
  display: 'Mesenteric artery US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_UpperExtremityVeinsUS: Coding = {
  code: '69395-2',
  display: 'Upper extremity veins US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForBiopsyOfSpinalCord: Coding = {
  code: '69396-0',
  display: 'US Guidance for biopsy of Spinal cord',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastVesselsUSDoppler: Coding = {
  code: '69397-8',
  display: 'Breast vessels US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityVesselsLeftUSDoppler: Coding = {
  code: '69398-6',
  display: 'Extremity vessels Left US.doppler',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FemoralVeinAndPoplitealVeinUS: Coding = {
  code: '69399-4',
  display: 'Femoral vein and Popliteal vein US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForChorionicVillusSampling: Coding = {
  code: '69400-0',
  display: 'US Guidance for chorionic villus sampling',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForNeedleBiopsyOfSpinalCord: Coding = {
  code: '69401-8',
  display: 'US Guidance for needle biopsy of Spinal cord',
  system: 'http://loinc.org',
};
const C80DocTypecodes_KidneyBilateralAndBladderUS: Coding = {
  code: '69402-6',
  display: 'Kidney Bilateral and Bladder US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USStandardCertificateOfDeath2003Revision: Coding = {
  code: '69409-1',
  display: 'U.S. standard certificate of death - 2003 revision',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ReferralNoteForensicMedicine: Coding = {
  code: '69438-0',
  display: 'Referral note Forensic medicine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PopulationStratificationDescriptionNarrative: Coding = {
  code: '69669-0',
  display: 'Population stratification description Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HealthQualityMeasureSupplementalDataNarrative: Coding = {
  code: '69670-8',
  display: 'Health quality measure supplemental data Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelStatementOfIdentitySection: Coding = {
  code: '69718-5',
  display: 'FDA product label Statement of identity section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelHealthClaimSection: Coding = {
  code: '69719-3',
  display: 'FDA product label Health claim section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InstructionsTextNarrative: Coding = {
  code: '69730-0',
  display: 'Instructions [Text] Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertDiagramOfDevice: Coding = {
  code: '69758-1',
  display: 'FDA package insert Diagram of device',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertRisks: Coding = {
  code: '69759-9',
  display: 'FDA package insert Risks',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertCompatibleAccessories: Coding = {
  code: '69760-7',
  display: 'FDA package insert Compatible accessories',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertAlarms: Coding = {
  code: '69761-5',
  display: 'FDA package insert Alarms',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertTroubleshooting: Coding = {
  code: '69762-3',
  display: 'FDA package insert Troubleshooting',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertDisposalAndWasteHandling: Coding = {
  code: '69763-1',
  display: 'FDA package insert Disposal and waste handling',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DocumentType: Coding = {
  code: '69764-9',
  display: 'Document type',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VAL21DeoxycorticosteroneMolesVolumeInSerumOrPlasma: Coding = {
  code: '69799-5',
  display: '21-Deoxycorticosterone [Moles/volume] in Serum or Plasma',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdominalVesselsAndPelvisVesselsCTAngiogramWContrastIV: Coding = {
  code: '69908-2',
  display: 'Abdominal vessels and Pelvis vessels CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AsthmaActionPlan: Coding = {
  code: '69981-9',
  display: 'Asthma action plan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DiagnosticStudyNote: Coding = {
  code: '70004-7',
  display: 'Diagnostic study note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EvaluationAndManagementOfSmokingCessation: Coding = {
  code: '70005-4',
  display: 'Evaluation and management of smoking cessation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MedicationManagementNote: Coding = {
  code: '70006-2',
  display: 'Medication management note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RestraintNote: Coding = {
  code: '70007-0',
  display: 'Restraint note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TransplantSurgeryHospitalProgressNote: Coding = {
  code: '70238-1',
  display: 'Transplant surgery Hospital Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForCSFAspirationOfSpineCervical: Coding = {
  code: '70915-4',
  display: 'US Guidance for CSF aspiration of Spine Cervical',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForCSFAspirationOfSpineLumbar: Coding = {
  code: '70916-2',
  display: 'US Guidance for CSF aspiration of Spine Lumbar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForCSFAspirationOfSpineThoracic: Coding = {
  code: '70917-0',
  display: 'US Guidance for CSF aspiration of Spine Thoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineCervical: Coding = {
  code: '70918-8',
  display: 'Fluoroscopy Guidance for injection of Spine Cervical',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineLumbar: Coding = {
  code: '70919-6',
  display: 'Fluoroscopy Guidance for injection of Spine Lumbar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineThoracic: Coding = {
  code: '70920-4',
  display: 'Fluoroscopy Guidance for injection of Spine Thoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNerveBlockOfSpineCervical: Coding = {
  code: '70921-2',
  display: 'CT Guidance for nerve block of Spine Cervical',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CTGuidanceForNerveBlockOfSpineThoracic: Coding = {
  code: '70922-0',
  display: 'CT Guidance for nerve block of Spine Thoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousVertebroplastyOfSpineCervical: Coding = {
  code: '70923-8',
  display: 'Fluoroscopy Guidance for percutaneous vertebroplasty of Spine Cervical',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousVertebroplastyOfSpineLumbar: Coding = {
  code: '70924-6',
  display: 'Fluoroscopy Guidance for percutaneous vertebroplasty of Spine Lumbar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPercutaneousVertebroplastyOfSpineThoracic: Coding = {
  code: '70925-3',
  display: 'Fluoroscopy Guidance for percutaneous vertebroplasty of Spine Thoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalUS: Coding = {
  code: '70926-1',
  display: 'Spine Cervical US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicUS: Coding = {
  code: '70927-9',
  display: 'Spine Thoracic US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineLumbarCTStereotactic: Coding = {
  code: '70928-7',
  display: 'Spine Lumbar CT stereotactic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineCervicalCTStereotactic: Coding = {
  code: '70929-5',
  display: 'Spine Cervical CT stereotactic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicCTStereotactic: Coding = {
  code: '70930-3',
  display: 'Spine Thoracic CT stereotactic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicCTWContrastIntradisc: Coding = {
  code: '70931-1',
  display: 'Spine Thoracic CT W contrast intradisc',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicXRaySingleViewPortable: Coding = {
  code: '70932-9',
  display: 'Spine Thoracic X-ray Single view portable',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SpineThoracicFluoroscopyWContrastIntradisc: Coding = {
  code: '70933-7',
  display: 'Spine Thoracic Fluoroscopy W contrast intradisc',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OcularHistoryNarrative: Coding = {
  code: '70934-5',
  display: 'Ocular history Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OphthalmicMedicationsNarrative: Coding = {
  code: '70935-2',
  display: 'Ophthalmic medications Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_VisionTestingNarrative: Coding = {
  code: '70936-0',
  display: 'Vision testing Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RefractiveMeasurementsNarrative: Coding = {
  code: '70938-6',
  display: 'Refractive measurements Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LensometryMeasurementsNarrative: Coding = {
  code: '70939-4',
  display: 'Lensometry measurements Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ConfrontationVisualFieldNarrative: Coding = {
  code: '70940-2',
  display: 'Confrontation visual field Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EyeExternalNarrative: Coding = {
  code: '70941-0',
  display: 'Eye external Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OcularAlignmentAndMotilityNarrative: Coding = {
  code: '70942-8',
  display: 'Ocular alignment and motility Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EyeAnteriorSegmentNarrative: Coding = {
  code: '70943-6',
  display: 'Eye anterior segment Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EyePosteriorSegmentNarrative: Coding = {
  code: '70944-4',
  display: 'Eye posterior segment Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_LacrimalNarrative: Coding = {
  code: '70945-1',
  display: 'Lacrimal Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AncillaryEyeTestsNarrative: Coding = {
  code: '70946-9',
  display: 'Ancillary eye tests Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OcularPhysicalExamNarrative: Coding = {
  code: '70948-5',
  display: 'Ocular physical exam Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PathologyReportSectionHeading: Coding = {
  code: '70949-3',
  display: 'Pathology report.section heading',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BirthCertificateDocument: Coding = {
  code: '71230-7',
  display: 'Birth certificate Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CMSPhysicalExamPanel: Coding = {
  code: '71388-3',
  display: 'CMS - physical exam panel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CMSReviewOfSystemsPanel: Coding = {
  code: '71406-3',
  display: 'CMS - review of systems panel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CMSPastFamilySocialHistoryPanel: Coding = {
  code: '71421-2',
  display: 'CMS - past family - social history panel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CMSHistoryOfPresentIllnessPanel: Coding = {
  code: '71428-7',
  display: 'CMS - history of present illness panel',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertIndexingBillingUnit: Coding = {
  code: '71446-9',
  display: 'FDA package insert Indexing - billing unit',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RiskAssessmentDocument: Coding = {
  code: '71482-4',
  display: 'Risk assessment Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPMICommonSideEffectsSection: Coding = {
  code: '71681-1',
  display: 'FDA package insert PMI - Common side effects section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPMIGetEmergencyMedicalHelpSection: Coding = {
  code: '71682-9',
  display: 'FDA package insert PMI - Get emergency medical help section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPMIStopTakingAndCallYourDoctorSection: Coding = {
  code: '71683-7',
  display: 'FDA package insert PMI - Stop taking and call your doctor section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPMIDirectionsForUseSection: Coding = {
  code: '71684-5',
  display: 'FDA package insert PMI - Directions for use section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPMITellYourDoctorBeforeTakingSection: Coding = {
  code: '71685-2',
  display: 'FDA package insert PMI - Tell your doctor before taking section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPMIDoNotTakeSection: Coding = {
  code: '71686-0',
  display: 'FDA package insert PMI - Do not take section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPMIImportantInformationSection: Coding = {
  code: '71687-8',
  display: 'FDA package insert PMI - Important information section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertPMIUsesSection: Coding = {
  code: '71688-6',
  display: 'FDA package insert PMI - Uses section',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelGenericDrugFacilityIdentificationSubmission: Coding = {
  code: '71743-9',
  display: 'FDA product label Generic drug facility identification submission',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertHealthCareProviderLetter: Coding = {
  code: '71744-7',
  display: 'FDA package insert Health care provider letter',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAProductLabelIdentificationOfCBERRegulatedGenericDrugFacility: Coding = {
  code: '72090-4',
  display: 'FDA product label Identification of CBER-regulated generic drug facility',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CancerEventReport: Coding = {
  code: '72134-0',
  display: 'Cancer event report',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CancerDiagnosisNarrative: Coding = {
  code: '72135-7',
  display: 'Cancer diagnosis Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightFFDMammogramTomosynthesisDiagnostic: Coding = {
  code: '72137-3',
  display: 'Breast - right FFD mammogram-tomosynthesis diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftFFDMammogramTomosynthesisDiagnostic: Coding = {
  code: '72138-1',
  display: 'Breast - left FFD mammogram-tomosynthesis diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralFFDMammogramTomosynthesisDiagnostic: Coding = {
  code: '72139-9',
  display: 'Breast - bilateral FFD mammogram-tomosynthesis diagnostic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastRightFFDMammogramTomosynthesisScreening: Coding = {
  code: '72140-7',
  display: 'Breast - right FFD mammogram-tomosynthesis screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastLeftFFDMammogramTomosynthesisScreening: Coding = {
  code: '72141-5',
  display: 'Breast - left FFD mammogram-tomosynthesis screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_BreastBilateralFFDMammogramTomosynthesisScreening: Coding = {
  code: '72142-3',
  display: 'Breast - bilateral FFD mammogram-tomosynthesis screening',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PermissionToReleaseImmunizationDataFromSchoolRecord: Coding = {
  code: '72169-6',
  display: 'Permission to release immunization data from school record',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PhotographicImageUnspecifiedBodyRegionDocument: Coding = {
  code: '72170-4',
  display: 'Photographic image Unspecified body region Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesRightMRIWAndWOContrastIV: Coding = {
  code: '72238-9',
  display: 'Toes - right MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesRightMRIWOContrast: Coding = {
  code: '72239-7',
  display: 'Toes - right MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesRightMRIWContrastIV: Coding = {
  code: '72240-5',
  display: 'Toes - right MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesLeftMRIWAndWOContrastIV: Coding = {
  code: '72241-3',
  display: 'Toes - left MRI W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesLeftMRIWOContrast: Coding = {
  code: '72242-1',
  display: 'Toes - left MRI WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ToesLeftMRIWContrastIV: Coding = {
  code: '72243-9',
  display: 'Toes - left MRI W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisMRIWAndWOContrastIVAndWEndorectalCoil: Coding = {
  code: '72244-7',
  display: 'Pelvis MRI W and WO contrast IV and W endorectal coil',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PelvisMRIWContrastPRAtRestAndMaxmalSphincterContractionDuringStrainingAndDefecation: Coding = {
  code: '72245-4',
  display: 'Pelvis MRI W contrast PR at rest and maxmal sphincter contraction during straining and defecation',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenAndPelvisMRIWContrastPOAndWAndWOContrastIV: Coding = {
  code: '72246-2',
  display: 'Abdomen and Pelvis MRI W contrast PO and W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenAndPelvisMRIWContrastPOAndWOContrastIV: Coding = {
  code: '72247-0',
  display: 'Abdomen and Pelvis MRI W contrast PO and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenMRCPWithAndWithoutContrastIV: Coding = {
  code: '72248-8',
  display: 'Abdomen MRCP with and without contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FacialBonesAndSinusesCTWOContrast: Coding = {
  code: '72249-6',
  display: 'Facial bones and Sinuses CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenAndPelvisCTWContrastPOAndWContrastIV: Coding = {
  code: '72250-4',
  display: 'Abdomen and Pelvis CT W contrast PO and W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestVesselsCTMultisectionForPulmonaryEmbolus: Coding = {
  code: '72251-2',
  display: 'Chest vessels CT Multisection for pulmonary embolus',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenAndPelvisCTWAndWOContrastIV: Coding = {
  code: '72252-0',
  display: 'Chest and Abdomen and Pelvis CT W and WO contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenAndPelvisCTWOContrast: Coding = {
  code: '72253-8',
  display: 'Chest and Abdomen and Pelvis CT WO contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ChestAndAbdomenAndPelvisCTWContrastIV: Coding = {
  code: '72254-6',
  display: 'Chest and Abdomen and Pelvis CT W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AortaAndFemoralArteryBilateralCTAngiogramWContrastIV: Coding = {
  code: '72255-3',
  display: 'Aorta and Femoral artery - bilateral CT angiogram W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AbdomenXRayForMotilityWithRadioopaqueMarkers: Coding = {
  code: '72256-1',
  display: 'Abdomen X-ray for motility with radioopaque markers',
  system: 'http://loinc.org',
};
const C80DocTypecodes_EvaluationOfMentalAndPhysicalIncapacityCertificateDocument: Coding = {
  code: '72267-8',
  display: 'Evaluation of mental and physical incapacity certificate Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ReportingRateReportingPeriodPopulationCalculated: Coding = {
  code: '72509-3',
  display: 'Reporting rate Reporting period population Calculated',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PerformanceRateReportingPeriodPopulationCalculated: Coding = {
  code: '72510-1',
  display: 'Performance rate Reporting period population Calculated',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AxillaRightUS: Coding = {
  code: '72528-3',
  display: 'Axilla - right US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AxillaLeftUS: Coding = {
  code: '72529-1',
  display: 'Axilla - left US',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForInjectionOfJoint: Coding = {
  code: '72530-9',
  display: 'US Guidance for injection of Joint',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RectumAndColonCT3DWContrastIVAndWAirContrastPR: Coding = {
  code: '72531-7',
  display: 'Rectum and Colon CT 3D W contrast IV and W air contrast PR',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAmbulatoryPhlebectomyOfExtremityVeinRight: Coding = {
  code: '72532-5',
  display: 'US Guidance for ambulatory phlebectomy of Extremity vein - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForAmbulatoryPhlebectomyOfExtremityVeinLeft: Coding = {
  code: '72533-3',
  display: 'US Guidance for ambulatory phlebectomy of Extremity vein - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForLaserAblationOfVeinSOfExtremityVeinRight: Coding = {
  code: '72534-1',
  display: 'US Guidance for laser ablation of vein(s) of Extremity vein - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForLaserAblationOfVeinSOfExtremityVeinLeft: Coding = {
  code: '72535-8',
  display: 'US Guidance for laser ablation of vein(s) of Extremity vein - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinsBilateral: Coding = {
  code: '72536-6',
  display: 'US Guidance for injection of sclerosing agent of Extremity veins - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinBilateral: Coding = {
  code: '72537-4',
  display: 'US Guidance for injection of sclerosing agent of Extremity vein - bilateral',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForRemovalOfLongtermPeripheralCatheterFromCentralVein: Coding = {
  code: '72538-2',
  display: 'Fluoroscopic angiogram Guidance for removal of longterm peripheral catheter from Central vein',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForPeripheralNerveDenervationOfUnspecifiedBodyRegion: Coding = {
  code: '72539-0',
  display: 'Fluoroscopy Guidance for peripheral nerve denervation of Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForFacetJointDenervationOfSpine: Coding = {
  code: '72540-8',
  display: 'Fluoroscopy Guidance for facet joint denervation of Spine',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForFacetJointDenervationOfSpineCervical: Coding = {
  code: '72541-6',
  display: 'Fluoroscopy Guidance for facet joint denervation of Spine Cervical',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForFacetJointDenervationOfSpineLumbar: Coding = {
  code: '72542-4',
  display: 'Fluoroscopy Guidance for facet joint denervation of Spine Lumbar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForIntercostalNerveDevervationOfSpineThoracic: Coding = {
  code: '72543-2',
  display: 'Fluoroscopy Guidance for intercostal nerve devervation of Spine Thoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForRemovalOfPercutaneousNephrostomyTubeFromKidneyBilateralWContrast: Coding = {
  code: '72544-0',
  display: 'Fluoroscopy Guidance for removal of percutaneous nephrostomy tube from Kidney - bilateral-- W contrast',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForReplacementOfPercutaneousDrainageTubeInBiliaryDuctsAndGallbladder: Coding = {
  code: '72545-7',
  display: 'Fluoroscopy Guidance for replacement of percutaneous drainage tube in Biliary ducts and Gallbladder',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForRemovalOfCVALumenObstructionFromCentralVein: Coding = {
  code: '72546-5',
  display: 'Fluoroscopy Guidance for removal of CVA lumen obstruction from Central vein',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForRemovalOfCVADeviceObstructionFromCentralVein: Coding = {
  code: '72547-3',
  display: 'Fluoroscopy Guidance for removal of CVA device obstruction from Central vein',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopicAngiogramGuidanceForRemovalOfCatheterFromCentralVeinWContrastIV: Coding = {
  code: '72548-1',
  display: 'Fluoroscopic angiogram Guidance for removal of catheter from Central vein-- W contrast IV',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForRemovalOfCatheterFromCentralVeinTunneled: Coding = {
  code: '72549-9',
  display: 'Fluoroscopy Guidance for removal of catheter from Central vein-- Tunneled',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForRepairOfCVACatheterWithPortOrPumpOfCentralVein: Coding = {
  code: '72550-7',
  display: 'Fluoroscopy Guidance for repair of CVA catheter with port or pump of Central vein',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForRepairOfCVACatheterWithoutPortOrPumpOfCentralVein: Coding = {
  code: '72551-5',
  display: 'Fluoroscopy Guidance for repair of CVA catheter without port or pump of Central vein',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForKyphoplastyOfSpineLumbar: Coding = {
  code: '72552-3',
  display: 'Fluoroscopy Guidance for kyphoplasty of Spine Lumbar',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForKyphoplastyOfSpineThoracic: Coding = {
  code: '72553-1',
  display: 'Fluoroscopy Guidance for kyphoplasty of Spine Thoracic',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FluoroscopyGuidanceForTriggerPointInjectionOfMuscle: Coding = {
  code: '72554-9',
  display: 'Fluoroscopy Guidance for trigger point injection of Muscle',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterventionalRadiologyConsultNote: Coding = {
  code: '72555-6',
  display: 'Interventional radiology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterventionalRadiologyProgressNote: Coding = {
  code: '72556-4',
  display: 'Interventional radiology Progress note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinsRight: Coding = {
  code: '72642-2',
  display: 'US Guidance for injection of sclerosing agent of Extremity veins - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinsLeft: Coding = {
  code: '72643-0',
  display: 'US Guidance for injection of sclerosing agent of Extremity veins - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinRight: Coding = {
  code: '72644-8',
  display: 'US Guidance for injection of sclerosing agent of Extremity vein - right',
  system: 'http://loinc.org',
};
const C80DocTypecodes_USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinLeft: Coding = {
  code: '72645-5',
  display: 'US Guidance for injection of sclerosing agent of Extremity vein - left',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteriesBilateralUSDopplerMultisectionAndPhysiologicArteryStudy: Coding = {
  code: '72830-3',
  display: 'Extremity arteries - bilateral US.doppler Multisection and physiologic artery study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteriesBilateralUSDopplerMultisectionLimitedAndPhysiologicArteryStudy: Coding = {
  code: '72831-1',
  display: 'Extremity arteries - bilateral US.doppler Multisection limited and physiologic artery study',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ExtremityArteriesBilateralUSDopplerMultisectionAndPhysiologicArteryStudyAtRestAndWithExercise: Coding = {
  code: '72832-9',
  display: 'Extremity arteries - bilateral US.doppler Multisection and physiologic artery study at rest and with exercise',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SurgicalSpecimenXRay: Coding = {
  code: '72876-6',
  display: 'Surgical specimen X-ray',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CommunicationOfCriticalResultsDescriptionDocument: Coding = {
  code: '73568-8',
  display: 'Communication of critical results [Description] Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiationExposureAndProtectionInformationDescriptionDocumentDiagnosticImaging: Coding = {
  code: '73569-6',
  display: 'Radiation exposure and protection information [Description] Document Diagnostic imaging',
  system: 'http://loinc.org',
};
const C80DocTypecodes_RadiologyConsultNote: Coding = {
  code: '73575-3',
  display: 'Radiology Consult note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrescriptionRequestPharmacyDocumentFromPharmacist: Coding = {
  code: '73709-8',
  display: 'Prescription request Pharmacy Document from Pharmacist',
  system: 'http://loinc.org',
};
const C80DocTypecodes_FDAPackageInsertIndexingProductConcept: Coding = {
  code: '73815-3',
  display: 'FDA package insert Indexing - product concept',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ReportSectionHeadingUnspecifiedBodyRegion: Coding = {
  code: '73983-9',
  display: 'Report.section heading Unspecified body region',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PeriodontalServiceAttachment: Coding = {
  code: '74030-8',
  display: 'Periodontal service attachment',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MeasureDescriptionNarrative: Coding = {
  code: '74045-6',
  display: 'Measure description Narrative',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ComplexMedicalConditionsActionPlan: Coding = {
  code: '74144-7',
  display: 'Complex medical conditions action plan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MultipleSclerosisActionPlan: Coding = {
  code: '74145-4',
  display: 'Multiple sclerosis action plan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HeartDiseaseActionPlan: Coding = {
  code: '74146-2',
  display: 'Heart disease action plan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_MuscularDystrophyActionPlan: Coding = {
  code: '74147-0',
  display: 'Muscular dystrophy action plan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_CysticFibrosisActionPlan: Coding = {
  code: '74148-8',
  display: 'Cystic fibrosis action plan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InflammatoryBowelDiseaseActionPlan: Coding = {
  code: '74149-6',
  display: 'Inflammatory bowel disease action plan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DiabetesTypeIActionPlan: Coding = {
  code: '74150-4',
  display: 'Diabetes type I action plan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DiabetesTypeIIActionPlan: Coding = {
  code: '74151-2',
  display: 'Diabetes type II action plan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AnaphylaxisActionPlan: Coding = {
  code: '74152-0',
  display: 'Anaphylaxis action plan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SeizureDisorderActionPlan: Coding = {
  code: '74153-8',
  display: 'Seizure disorder action plan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_AutismActionPlan: Coding = {
  code: '74154-6',
  display: 'Autism action plan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ADHDActionPlan: Coding = {
  code: '74155-3',
  display: 'ADHD action plan',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OncologyTreatmentPlanAndSummaryDocument: Coding = {
  code: '74156-1',
  display: 'Oncology treatment plan and summary Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_OccupationalSummaryNote: Coding = {
  code: '74166-0',
  display: 'Occupational summary note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterRAIEmergencyScreenerForPsychiatryESPDocument: Coding = {
  code: '74187-6',
  display: 'InterRAI Emergency Screener for Psychiatry (ESP) Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterRAIAcuteCareACHospitalDocument: Coding = {
  code: '74188-4',
  display: 'InterRAI Acute Care (AC) Hospital Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterRAIPalliativeCarePCDocument: Coding = {
  code: '74189-2',
  display: 'InterRAI Palliative Care (PC) Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterRAICommunityHealthAssessmentDeafblindSupplementCHADbDocument: Coding = {
  code: '74190-0',
  display: 'InterRAI Community Health Assessment - Deafblind Supplement (CHA-Db) Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterRAICommunityHealthAssessmentAssistedLivingSupplementCHAALDocument: Coding = {
  code: '74191-8',
  display: 'InterRAI Community Health Assessment - Assisted Living Supplement (CHA-AL) Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterRAICommunityHealthAssessmentMentalHealthSupplementCHAMHDocument: Coding = {
  code: '74192-6',
  display: 'InterRAI Community Health Assessment - Mental Health Supplement (CHA-MH) Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterRAICommunityHealthAssessmentFunctionalSupplementCHAFSDocument: Coding = {
  code: '74193-4',
  display: 'InterRAI Community Health Assessment - Functional Supplement (CHA-FS) Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterRAICommunityHealthAssessmentCHADocument: Coding = {
  code: '74194-2',
  display: 'InterRAI Community Health Assessment (CHA) Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterRAILongTermCareFacilityLTCFDocument: Coding = {
  code: '74195-9',
  display: 'InterRAI Long Term Care Facility (LTCF) Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterRAIHomeCareHCDocument: Coding = {
  code: '74196-7',
  display: 'InterRAI Home Care (HC) Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InterRAIContactAssessmentCADocument: Coding = {
  code: '74197-5',
  display: 'InterRAI Contact Assessment (CA) Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_TraumaSummaryRegistryReportDocument: Coding = {
  code: '74198-3',
  display: 'Trauma summary registry report Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_PrehospitalSummaryDocument: Coding = {
  code: '74207-2',
  display: 'Prehospital summary Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DemographicInformationPlusHistoryOfOccupationDocument: Coding = {
  code: '74208-0',
  display: 'Demographic information + History of occupation Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_InjuryEventSummaryDocument: Coding = {
  code: '74209-8',
  display: 'Injury event summary Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_SummaryOfEpisodeNoteEmergencyDepartmentPlusHospital: Coding = {
  code: '74211-4',
  display: 'Summary of episode note Emergency department+Hospital',
  system: 'http://loinc.org',
};
const C80DocTypecodes_DischargeInstructions: Coding = {
  code: '74213-0',
  display: 'Discharge instructions',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HIVSummaryRegistryReportDocument: Coding = {
  code: '74264-3',
  display: 'HIV summary registry report Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_IndividualCounselingNote: Coding = {
  code: '74282-5',
  display: 'Individual counseling note',
  system: 'http://loinc.org',
};
const C80DocTypecodes_QuestionnaireResponseSectionDocument: Coding = {
  code: '74465-6',
  display: 'Questionnaire response section Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_QuestionnaireFormDefinitionSectionDocument: Coding = {
  code: '74468-0',
  display: 'Questionnaire form definition section Document',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalDocumentSettingFromLOINCDocumentOntology: Coding = {
  code: '74476-3',
  display: 'Clinical document Setting from LOINC Document Ontology',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalDocumentKindOfDocumentFromLOINCDocumentOntology: Coding = {
  code: '74477-1',
  display: 'Clinical document Kind of document from LOINC Document Ontology',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalDocumentTypeOfServiceFromLOINCDocumentOntology: Coding = {
  code: '74478-9',
  display: 'Clinical document Type of service from LOINC Document Ontology',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalDocumentRoleFromLOINCDocumentOntology: Coding = {
  code: '74479-7',
  display: 'Clinical document Role from LOINC Document Ontology',
  system: 'http://loinc.org',
};
const C80DocTypecodes_ClinicalDocumentSubjectMatterDomainFromLOINCDocumentOntology: Coding = {
  code: '74480-5',
  display: 'Clinical document Subject matter domain from LOINC Document Ontology',
  system: 'http://loinc.org',
};
const C80DocTypecodes_HospitalDischargeInstructions: Coding = {
  code: '8653-8',
  display: 'Hospital Discharge instructions',
  system: 'http://loinc.org',
};
/**
 * This is the code specifying the precise type of document (e.g. Pulmonary History and  Physical, Discharge Summary, Ultrasound Report, etc.). The Document Type value set includes all LOINC  values listed in HITSP C80 Table 2-144 Document Class Value Set Definition above used for Document Class,  and all LOINC values whose SCALE is DOC in the LOINC database.
 */
export const C80DocTypecodes = {
  VAL18HydroxydeoxycorticosteroneMassVolumeInSerumOrPlasma: C80DocTypecodes_VAL18HydroxydeoxycorticosteroneMassVolumeInSerumOrPlasma,
  AnesthesiaRecords: C80DocTypecodes_AnesthesiaRecords,
  ChemotherapyRecords: C80DocTypecodes_ChemotherapyRecords,
  ConsultNote: C80DocTypecodes_ConsultNote,
  PhysicianDischargeSummary: C80DocTypecodes_PhysicianDischargeSummary,
  ProviderUnspecifedHistoryAndPhysicalNote: C80DocTypecodes_ProviderUnspecifedHistoryAndPhysicalNote,
  PhysicianInitialAssessmentNoteAtFirstEncounter: C80DocTypecodes_PhysicianInitialAssessmentNoteAtFirstEncounter,
  PhysicalTherapyInitialAssessmentNoteAtFirstEncounter: C80DocTypecodes_PhysicalTherapyInitialAssessmentNoteAtFirstEncounter,
  PodiatryInitialAssessmentNoteAtFirstEncounter: C80DocTypecodes_PodiatryInitialAssessmentNoteAtFirstEncounter,
  PsychologyInitialAssessmentNoteAtFirstEncounter: C80DocTypecodes_PsychologyInitialAssessmentNoteAtFirstEncounter,
  SocialWorkInitialAssessmentNoteAtFirstEncounter: C80DocTypecodes_SocialWorkInitialAssessmentNoteAtFirstEncounter,
  OccupationalTherapyInitialAssessmentNoteAtFirstEncounter: C80DocTypecodes_OccupationalTherapyInitialAssessmentNoteAtFirstEncounter,
  LaboratoryReport: C80DocTypecodes_LaboratoryReport,
  MedicalRecords: C80DocTypecodes_MedicalRecords,
  ProviderUnspecifiedOperationNote: C80DocTypecodes_ProviderUnspecifiedOperationNote,
  PhysicianProcedureNote: C80DocTypecodes_PhysicianProcedureNote,
  ProviderUnspecifiedProgressNote: C80DocTypecodes_ProviderUnspecifiedProgressNote,
  OccupationalTherapyProgressNote: C80DocTypecodes_OccupationalTherapyProgressNote,
  PhysicalTherapyProgressNote: C80DocTypecodes_PhysicalTherapyProgressNote,
  PodiatryProgressNote: C80DocTypecodes_PodiatryProgressNote,
  PsychologyProgressNote: C80DocTypecodes_PsychologyProgressNote,
  SpeechLanguagePathologyProgressNote: C80DocTypecodes_SpeechLanguagePathologyProgressNote,
  ChiropracticRecordsTotalEncounter: C80DocTypecodes_ChiropracticRecordsTotalEncounter,
  PhysicalTherapyRecordsTotalEncounter: C80DocTypecodes_PhysicalTherapyRecordsTotalEncounter,
  PhysicianRecordsTotalEncounter: C80DocTypecodes_PhysicianRecordsTotalEncounter,
  PodiatryRecordsTotalEncounter: C80DocTypecodes_PodiatryRecordsTotalEncounter,
  PsychologyRecordsTotalEncounter: C80DocTypecodes_PsychologyRecordsTotalEncounter,
  SocialServiceRecordsTotalEncounter: C80DocTypecodes_SocialServiceRecordsTotalEncounter,
  SpeechTherapyRecordsTotalEncounter: C80DocTypecodes_SpeechTherapyRecordsTotalEncounter,
  OccupationalTherapyRecordsTotalEncounter: C80DocTypecodes_OccupationalTherapyRecordsTotalEncounter,
  EEGStudy: C80DocTypecodes_EEGStudy,
  EKGStudy: C80DocTypecodes_EKGStudy,
  USPelvisAndFetusForPregnancy: C80DocTypecodes_USPelvisAndFetusForPregnancy,
  PathologyStudy: C80DocTypecodes_PathologyStudy,
  PsychiatryStudy: C80DocTypecodes_PsychiatryStudy,
  SurgicalPathologyStudy: C80DocTypecodes_SurgicalPathologyStudy,
  TemperatureCharts: C80DocTypecodes_TemperatureCharts,
  NurseNotes: C80DocTypecodes_NurseNotes,
  MRIBrainStudy: C80DocTypecodes_MRIBrainStudy,
  NurseryRecords: C80DocTypecodes_NurseryRecords,
  VAL18HydroxydeoxycortisolCreatinineMassRatioInUrine: C80DocTypecodes_VAL18HydroxydeoxycortisolCreatinineMassRatioInUrine,
  ProviderUnspecifiedEDProgressNote: C80DocTypecodes_ProviderUnspecifiedEDProgressNote,
  LaborAndDeliveryRecords: C80DocTypecodes_LaborAndDeliveryRecords,
  VAL11DeoxycorticosteroneMassTimeIn24HourUrine: C80DocTypecodes_VAL11DeoxycorticosteroneMassTimeIn24HourUrine,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasmaPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasmaPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma,
  ThyroidScanStudyReport: C80DocTypecodes_ThyroidScanStudyReport,
  PsychiatricServiceAttachment: C80DocTypecodes_PsychiatricServiceAttachment,
  PhysicianAttendingProgressNote: C80DocTypecodes_PhysicianAttendingProgressNote,
  OccupationalTherapyInitialAssessmentNote: C80DocTypecodes_OccupationalTherapyInitialAssessmentNote,
  PhysicalTherapyInitialAssessmentNote: C80DocTypecodes_PhysicalTherapyInitialAssessmentNote,
  PhysicianInitialAssessmentNote: C80DocTypecodes_PhysicianInitialAssessmentNote,
  PodiatryInitialAssessmentNote: C80DocTypecodes_PodiatryInitialAssessmentNote,
  PsychologyInitialAssessmentNote: C80DocTypecodes_PsychologyInitialAssessmentNote,
  SocialWorkInitialAssessmentNote: C80DocTypecodes_SocialWorkInitialAssessmentNote,
  SpeechLanguagePathologyInitialAssessmentNote: C80DocTypecodes_SpeechLanguagePathologyInitialAssessmentNote,
  ArthroscopyStudy: C80DocTypecodes_ArthroscopyStudy,
  AutopsyReport: C80DocTypecodes_AutopsyReport,
  BronchoscopyStudy: C80DocTypecodes_BronchoscopyStudy,
  CardiacCatheterizationStudy: C80DocTypecodes_CardiacCatheterizationStudy,
  ColonoscopyStudy: C80DocTypecodes_ColonoscopyStudy,
  DiagnosticImagingStudy: C80DocTypecodes_DiagnosticImagingStudy,
  ElectromyogramStudy: C80DocTypecodes_ElectromyogramStudy,
  ElectrophysiologyStudy: C80DocTypecodes_ElectrophysiologyStudy,
  EndoscopyStudy: C80DocTypecodes_EndoscopyStudy,
  ExerciseStressTestStudy: C80DocTypecodes_ExerciseStressTestStudy,
  FlexibleSigmoidoscopyStudy: C80DocTypecodes_FlexibleSigmoidoscopyStudy,
  HolterMonitorStudy: C80DocTypecodes_HolterMonitorStudy,
  MRISpineStudy: C80DocTypecodes_MRISpineStudy,
  SpirometryStudy: C80DocTypecodes_SpirometryStudy,
  ProviderUnspecifiedTransferSummary: C80DocTypecodes_ProviderUnspecifiedTransferSummary,
  PhysicianConsultingInitialAssessmentNote: C80DocTypecodes_PhysicianConsultingInitialAssessmentNote,
  AlcoholAndOrSubstanceAbuseServiceAttachment: C80DocTypecodes_AlcoholAndOrSubstanceAbuseServiceAttachment,
  CardiacServiceAttachment: C80DocTypecodes_CardiacServiceAttachment,
  MedicalSocialServicesAttachment: C80DocTypecodes_MedicalSocialServicesAttachment,
  OccupationalTherapyServiceAttachment: C80DocTypecodes_OccupationalTherapyServiceAttachment,
  CardiacStressStudyProcedure: C80DocTypecodes_CardiacStressStudyProcedure,
  HospitalConsultationsDocument: C80DocTypecodes_HospitalConsultationsDocument,
  DischargeSummary: C80DocTypecodes_DischargeSummary,
  PhysicalTherapyServiceAttachment: C80DocTypecodes_PhysicalTherapyServiceAttachment,
  RespiratoryTherapyServiceAttachment: C80DocTypecodes_RespiratoryTherapyServiceAttachment,
  SkilledNursingServiceAttachment: C80DocTypecodes_SkilledNursingServiceAttachment,
  SourceOfDocumentUsedToAbstractCancer: C80DocTypecodes_SourceOfDocumentUsedToAbstractCancer,
  AbdomenRetroperitoneumUS: C80DocTypecodes_AbdomenRetroperitoneumUS,
  AbdomenRUQUS: C80DocTypecodes_AbdomenRUQUS,
  AbdominalVesselsMRIAngiogramWContrastIV: C80DocTypecodes_AbdominalVesselsMRIAngiogramWContrastIV,
  AbdominalVesselsUSDoppler: C80DocTypecodes_AbdominalVesselsUSDoppler,
  AcetabulumXRay: C80DocTypecodes_AcetabulumXRay,
  AcromioclavicularJointXRay: C80DocTypecodes_AcromioclavicularJointXRay,
  USGuidanceForRemovalOfAmnioticFluidFromUterus: C80DocTypecodes_USGuidanceForRemovalOfAmnioticFluidFromUterus,
  AnkleMRI: C80DocTypecodes_AnkleMRI,
  AnkleMRIWAndWOContrastIV: C80DocTypecodes_AnkleMRIWAndWOContrastIV,
  AnkleXRay2Views: C80DocTypecodes_AnkleXRay2Views,
  AnkleXRay: C80DocTypecodes_AnkleXRay,
  AnusUS: C80DocTypecodes_AnusUS,
  AortaFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_AortaFluoroscopicAngiogramAngioplastyWContrastIA,
  AortaThoracicCT: C80DocTypecodes_AortaThoracicCT,
  AortaThoracicCTWContrastIV: C80DocTypecodes_AortaThoracicCTWContrastIV,
  AortaArchAndNeckFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AortaArchAndNeckFluoroscopicAngiogramWContrastIA,
  AortaUS: C80DocTypecodes_AortaUS,
  AppendixUS: C80DocTypecodes_AppendixUS,
  UpperExtremityVesselsMRIAngiogramWContrastIV: C80DocTypecodes_UpperExtremityVesselsMRIAngiogramWContrastIV,
  UpperExtremityVeinsFluoroscopicAngiogramWContrastIV: C80DocTypecodes_UpperExtremityVeinsFluoroscopicAngiogramWContrastIV,
  AVFistulaFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AVFistulaFluoroscopicAngiogramWContrastIA,
  StentFluoroscopyWContrastIntraStent: C80DocTypecodes_StentFluoroscopyWContrastIntraStent,
  VesselIntracranialFluoroscopicAngiogramEmbolectomyWContrastIV: C80DocTypecodes_VesselIntracranialFluoroscopicAngiogramEmbolectomyWContrastIV,
  ArteryFluoroscopicAngiogramEmbolizationWContrastIA: C80DocTypecodes_ArteryFluoroscopicAngiogramEmbolizationWContrastIA,
  FluoroscopicAngiogramGuidanceForPlacementOfStentInArtery: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInArtery,
  AbdomenMRI: C80DocTypecodes_AbdomenMRI,
  AbdomenMRIWAndWOContrastIV: C80DocTypecodes_AbdomenMRIWAndWOContrastIV,
  AbdomenUS: C80DocTypecodes_AbdomenUS,
  USGuidanceForRemovalOfFluidFromAbdomen: C80DocTypecodes_USGuidanceForRemovalOfFluidFromAbdomen,
  AbdomenXRayAPLeftLateralDecubitusPortable: C80DocTypecodes_AbdomenXRayAPLeftLateralDecubitusPortable,
  AbdomenXRayAPLeftLateralDecubitus: C80DocTypecodes_AbdomenXRayAPLeftLateralDecubitus,
  AbdomenXRayAPLeftLateralDecubitusAndRightLateralDecubitus: C80DocTypecodes_AbdomenXRayAPLeftLateralDecubitusAndRightLateralDecubitus,
  AbdomenXRayAPRightLateralDecubitus: C80DocTypecodes_AbdomenXRayAPRightLateralDecubitus,
  AbdomenXRayAPUprightPortable: C80DocTypecodes_AbdomenXRayAPUprightPortable,
  AbdomenRetroperitoneumCT: C80DocTypecodes_AbdomenRetroperitoneumCT,
  AbdomenRetroperitoneumCTWContrast: C80DocTypecodes_AbdomenRetroperitoneumCTWContrast,
  AVFistulaFluoroscopicAngiogramAtherectomyWContrastIV: C80DocTypecodes_AVFistulaFluoroscopicAngiogramAtherectomyWContrastIV,
  AVShuntFluoroscopicAngiogramWContrastIV: C80DocTypecodes_AVShuntFluoroscopicAngiogramWContrastIV,
  FluoroscopyGuidanceForStoneRemovalOfBiliaryDuctCommonWContrastIntraBiliaryDuct: C80DocTypecodes_FluoroscopyGuidanceForStoneRemovalOfBiliaryDuctCommonWContrastIntraBiliaryDuct,
  BiliaryDuctsAndGallbladderScanForPatencyOfBiliaryStructuresAndEjectionFractionWSincalideAndWRadionuclideIV: C80DocTypecodes_BiliaryDuctsAndGallbladderScanForPatencyOfBiliaryStructuresAndEjectionFractionWSincalideAndWRadionuclideIV,
  BiliaryDuctsAndGallbladderScanForPatencyOfBiliaryStructuresWTc99mIV: C80DocTypecodes_BiliaryDuctsAndGallbladderScanForPatencyOfBiliaryStructuresWTc99mIV,
  BiliaryDuctsAndGallbladderXRayWContrastIV: C80DocTypecodes_BiliaryDuctsAndGallbladderXRayWContrastIV,
  BiliaryDuctsAndGallbladderFluoroscopyDuringSurgeryWContrastBiliaryDuct: C80DocTypecodes_BiliaryDuctsAndGallbladderFluoroscopyDuringSurgeryWContrastBiliaryDuct,
  BiliaryDuctsAndGallbladderFluoroscopyWContrastPercutaneousTranshepatic: C80DocTypecodes_BiliaryDuctsAndGallbladderFluoroscopyWContrastPercutaneousTranshepatic,
  UrinaryBladderArteriesFluoroscopicAngiogramWContrastIA: C80DocTypecodes_UrinaryBladderArteriesFluoroscopicAngiogramWContrastIA,
  BoneXRayDuringSurgery: C80DocTypecodes_BoneXRayDuringSurgery,
  BonesSPECT: C80DocTypecodes_BonesSPECT,
  BonesLongXRaySurvey: C80DocTypecodes_BonesLongXRaySurvey,
  BrachiocephalicArteryFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_BrachiocephalicArteryFluoroscopicAngiogramAngioplastyWContrastIA,
  BrachialArteryAndSubclavianArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_BrachialArteryAndSubclavianArteryFluoroscopicAngiogramWContrastIA,
  ThoracicOutletMRI: C80DocTypecodes_ThoracicOutletMRI,
  ThoracicOutletMRIWAndWOContrastIV: C80DocTypecodes_ThoracicOutletMRIWAndWOContrastIV,
  ThoracicOutletVesselsMRIAngiogramWContrastIV: C80DocTypecodes_ThoracicOutletVesselsMRIAngiogramWContrastIV,
  CTGuidanceStereotacticForBiopsyOfHeadWContrastIV: C80DocTypecodes_CTGuidanceStereotacticForBiopsyOfHeadWContrastIV,
  BrainMRIWAnesthesia: C80DocTypecodes_BrainMRIWAnesthesia,
  BrainMRIWAndWOContrastIV: C80DocTypecodes_BrainMRIWAndWOContrastIV,
  BrainMRIWAndWOContrastIVAndWAnesthesia: C80DocTypecodes_BrainMRIWAndWOContrastIVAndWAnesthesia,
  BrainMRIWContrastIV: C80DocTypecodes_BrainMRIWContrastIV,
  BrainMRI: C80DocTypecodes_BrainMRI,
  BrainScanBrainDeathProtocolWTc99mHMPAOIV: C80DocTypecodes_BrainScanBrainDeathProtocolWTc99mHMPAOIV,
  HeadVesselsMRIAngiogramWContrastIV: C80DocTypecodes_HeadVesselsMRIAngiogramWContrastIV,
  MammogramGuidanceForAspirationOfCystOfBreast: C80DocTypecodes_MammogramGuidanceForAspirationOfCystOfBreast,
  MammogramGuidanceForNeedleLocalizationOfMassOfBreast: C80DocTypecodes_MammogramGuidanceForNeedleLocalizationOfMassOfBreast,
  BreastSpecimenUS: C80DocTypecodes_BreastSpecimenUS,
  BreastSpecimenMammogram: C80DocTypecodes_BreastSpecimenMammogram,
  MammogramGuidanceForAspirationOfBreast: C80DocTypecodes_MammogramGuidanceForAspirationOfBreast,
  BreastUSLimited: C80DocTypecodes_BreastUSLimited,
  USGuidanceForNeedleLocalizationOfBreast: C80DocTypecodes_USGuidanceForNeedleLocalizationOfBreast,
  BreastUS: C80DocTypecodes_BreastUS,
  MammogramGuidanceForBiopsyOfBreast: C80DocTypecodes_MammogramGuidanceForBiopsyOfBreast,
  MammogramGuidanceStereotacticForBiopsyOfBreast: C80DocTypecodes_MammogramGuidanceStereotacticForBiopsyOfBreast,
  BreastMammogramDiagnosticLimited: C80DocTypecodes_BreastMammogramDiagnosticLimited,
  BreastMammogramDiagnostic: C80DocTypecodes_BreastMammogramDiagnostic,
  BreastMammogramScreening: C80DocTypecodes_BreastMammogramScreening,
  MammogramGuidanceForCoreNeedlePercutaneousBiopsyOfBreast: C80DocTypecodes_MammogramGuidanceForCoreNeedlePercutaneousBiopsyOfBreast,
  BreastMammogramLimited: C80DocTypecodes_BreastMammogramLimited,
  OutpatientConsultation2ndOpinion: C80DocTypecodes_OutpatientConsultation2ndOpinion,
  CalcaneusXRay: C80DocTypecodes_CalcaneusXRay,
  FluoroscopicAngiogramGuidanceForPlacementOfCatheterInArteryInCentralCardiovascularArtery: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInArteryInCentralCardiovascularArtery,
  CarotidArteryExtracranialFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_CarotidArteryExtracranialFluoroscopicAngiogramAngioplastyWContrastIA,
  CarotidArteryIntracranialFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_CarotidArteryIntracranialFluoroscopicAngiogramAngioplastyWContrastIA,
  CarotidArteryUS: C80DocTypecodes_CarotidArteryUS,
  CarotidArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryFluoroscopicAngiogramWContrastIA,
  WristXRay: C80DocTypecodes_WristXRay,
  CatheterFluoroscopyPatencyCheckWContrastViaCatheter: C80DocTypecodes_CatheterFluoroscopyPatencyCheckWContrastViaCatheter,
  FluoroscopyGuidanceForPercutaneousDrainageOfCavity: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfCavity,
  CeliacArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CeliacArteryFluoroscopicAngiogramWContrastIA,
  CTGuidanceForAnestheticBlockInjectionOfCeliacPlexus: C80DocTypecodes_CTGuidanceForAnestheticBlockInjectionOfCeliacPlexus,
  FluoroscopicAngiogramGuidanceForChangeOfCentralCatheterInCentralVeinWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForChangeOfCentralCatheterInCentralVeinWContrastIV,
  FluoroscopicAngiogramGuidanceForPlacementOfCatheterInCentralVeinWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInCentralVeinWContrastIV,
  FluoroscopicAngiogramGuidanceForRepositionOfCatheterInCentralVeinWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForRepositionOfCatheterInCentralVeinWContrastIV,
  ChestCT: C80DocTypecodes_ChestCT,
  ChestCTWContrastIV: C80DocTypecodes_ChestCTWContrastIV,
  ChestMRI: C80DocTypecodes_ChestMRI,
  ChestUS: C80DocTypecodes_ChestUS,
  UnspecifiedBodyRegionFluoroscopyCentralVeinCatheterPlacementCheck: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyCentralVeinCatheterPlacementCheck,
  ChestXRayAPPortable: C80DocTypecodes_ChestXRayAPPortable,
  ChestXRayPortableWInspirationAndExpiration: C80DocTypecodes_ChestXRayPortableWInspirationAndExpiration,
  ChestXRayPAUprightWInspirationAndExpiration: C80DocTypecodes_ChestXRayPAUprightWInspirationAndExpiration,
  ChestXRayAPLeftLateralDecubitusPortable: C80DocTypecodes_ChestXRayAPLeftLateralDecubitusPortable,
  ChestXRayAPLeftLateralDecubitus: C80DocTypecodes_ChestXRayAPLeftLateralDecubitus,
  ChestXRayLeftLateralUprightPortable: C80DocTypecodes_ChestXRayLeftLateralUprightPortable,
  ChestXRayLeftLateralUpright: C80DocTypecodes_ChestXRayLeftLateralUpright,
  ChestXRayLordotic: C80DocTypecodes_ChestXRayLordotic,
  ChestXRayLeftObliquePortable: C80DocTypecodes_ChestXRayLeftObliquePortable,
  ChestXRayAPAndPAUpright: C80DocTypecodes_ChestXRayAPAndPAUpright,
  ChestXRayPAAndLateralAndRightOrLeftObliqueUpright: C80DocTypecodes_ChestXRayPAAndLateralAndRightOrLeftObliqueUpright,
  ChestXRayPAAndLateralUprightPortable: C80DocTypecodes_ChestXRayPAAndLateralUprightPortable,
  ChestXRayPAAndRightLateralAndRightObliqueAndLeftObliqueUprightPortable: C80DocTypecodes_ChestXRayPAAndRightLateralAndRightObliqueAndLeftObliqueUprightPortable,
  ChestXRayPAAndRightLateralAndRightObliqueAndLeftObliqueUpright: C80DocTypecodes_ChestXRayPAAndRightLateralAndRightObliqueAndLeftObliqueUpright,
  ChestXRayPAAndLateralUpright: C80DocTypecodes_ChestXRayPAAndLateralUpright,
  ChestXRayPAUpright: C80DocTypecodes_ChestXRayPAUpright,
  ChestXRayAPRightLateralDecubitusAndLeftLateralDecubitusPortable: C80DocTypecodes_ChestXRayAPRightLateralDecubitusAndLeftLateralDecubitusPortable,
  ChestXRayAPRightLateralDecubitusAndLeftLateralDecubitus: C80DocTypecodes_ChestXRayAPRightLateralDecubitusAndLeftLateralDecubitus,
  ChestXRayRightObliqueAndLeftObliqueUpright: C80DocTypecodes_ChestXRayRightObliqueAndLeftObliqueUpright,
  ChestXRayAPRightLateralDecubitusPortable: C80DocTypecodes_ChestXRayAPRightLateralDecubitusPortable,
  ChestXRayAPAndAPRightLateralDecubitus: C80DocTypecodes_ChestXRayAPAndAPRightLateralDecubitus,
  ChestXRayAPAndAPRightLateralDecubitusPortable: C80DocTypecodes_ChestXRayAPAndAPRightLateralDecubitusPortable,
  ChestFluoroscopyImageIntensifierDuringSurgery: C80DocTypecodes_ChestFluoroscopyImageIntensifierDuringSurgery,
  ChestFluoroscopyDuringSurgery: C80DocTypecodes_ChestFluoroscopyDuringSurgery,
  ChestXRayTomograph: C80DocTypecodes_ChestXRayTomograph,
  AortaFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AortaFluoroscopicAngiogramWContrastIA,
  ChestVesselsMRIAngiogramWContrastIV: C80DocTypecodes_ChestVesselsMRIAngiogramWContrastIV,
  AortaThoracicMRIAngiogram: C80DocTypecodes_AortaThoracicMRIAngiogram,
  PleuralSpaceFluoroscopyWContrastIntraPleuralSpace: C80DocTypecodes_PleuralSpaceFluoroscopyWContrastIntraPleuralSpace,
  USGuidanceForAspirationOfPleuralSpace: C80DocTypecodes_USGuidanceForAspirationOfPleuralSpace,
  HeadCisternScanWRadionuclideIT: C80DocTypecodes_HeadCisternScanWRadionuclideIT,
  ClavicleXRay: C80DocTypecodes_ClavicleXRay,
  SacrumAndCoccyxXRay: C80DocTypecodes_SacrumAndCoccyxXRay,
  ColonFluoroscopyWAirAndBariumContrastPR: C80DocTypecodes_ColonFluoroscopyWAirAndBariumContrastPR,
  ColonFluoroscopyWContrastPR: C80DocTypecodes_ColonFluoroscopyWContrastPR,
  ColonFluoroscopyTransitPostSolidContrast: C80DocTypecodes_ColonFluoroscopyTransitPostSolidContrast,
  ColonFluoroscopyWWaterSolubleContrastPR: C80DocTypecodes_ColonFluoroscopyWWaterSolubleContrastPR,
  USGuidanceForBiopsyOfCystOfUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForBiopsyOfCystOfUnspecifiedBodyRegion,
  FluoroscopyGuidanceForAspirationOfCystOfUnspecifiedBodyRegion: C80DocTypecodes_FluoroscopyGuidanceForAspirationOfCystOfUnspecifiedBodyRegion,
  DiaphragmUSMotion: C80DocTypecodes_DiaphragmUSMotion,
  DuodenumFluoroscopyWContrastPOAndHypotonicAgentPerNg: C80DocTypecodes_DuodenumFluoroscopyWContrastPOAndHypotonicAgentPerNg,
  ElbowMRI: C80DocTypecodes_ElbowMRI,
  ElbowMRIWAndWOContrastIV: C80DocTypecodes_ElbowMRIWAndWOContrastIV,
  ElbowXRay: C80DocTypecodes_ElbowXRay,
  PelvisUSTransvaginal: C80DocTypecodes_PelvisUSTransvaginal,
  EsophagusFluoroscopyWContrastPO: C80DocTypecodes_EsophagusFluoroscopyWContrastPO,
  EsophagusFluoroscopyWGastrografinPO: C80DocTypecodes_EsophagusFluoroscopyWGastrografinPO,
  FluoroscopyGuidanceForDilationOfEsophagus: C80DocTypecodes_FluoroscopyGuidanceForDilationOfEsophagus,
  EsophagusAndHypopharynxFluoroscopyVideoWContrastPODuringSwallowing: C80DocTypecodes_EsophagusAndHypopharynxFluoroscopyVideoWContrastPODuringSwallowing,
  EsophagusAndHypopharynxFluoroscopyVideoWLiquidAndPasteContrastPODuringSwallowing: C80DocTypecodes_EsophagusAndHypopharynxFluoroscopyVideoWLiquidAndPasteContrastPODuringSwallowing,
  EsophagusAndStomachScanWTc99mSCPO: C80DocTypecodes_EsophagusAndStomachScanWTc99mSCPO,
  ExtracranialVesselsFluoroscopicAngiogramEmbolectomyWContrastIA: C80DocTypecodes_ExtracranialVesselsFluoroscopicAngiogramEmbolectomyWContrastIA,
  PeripheralVeinsFluoroscopicAngiogramWContrastIV: C80DocTypecodes_PeripheralVeinsFluoroscopicAngiogramWContrastIV,
  LowerExtremityXRay: C80DocTypecodes_LowerExtremityXRay,
  LowerExtremityJointMRI: C80DocTypecodes_LowerExtremityJointMRI,
  UpperExtremityMRI: C80DocTypecodes_UpperExtremityMRI,
  UpperExtremityXRay: C80DocTypecodes_UpperExtremityXRay,
  ExtremityCT: C80DocTypecodes_ExtremityCT,
  ExtremityCTWContrastIV: C80DocTypecodes_ExtremityCTWContrastIV,
  USGuidanceForDrainageOfExtremity: C80DocTypecodes_USGuidanceForDrainageOfExtremity,
  ExtremityUS: C80DocTypecodes_ExtremityUS,
  FaceMRIWAndWOContrastIV: C80DocTypecodes_FaceMRIWAndWOContrastIV,
  FacialBonesXRay: C80DocTypecodes_FacialBonesXRay,
  FacialBonesAndSinusesCT: C80DocTypecodes_FacialBonesAndSinusesCT,
  FacialBonesAndSinusesCTWContrastIV: C80DocTypecodes_FacialBonesAndSinusesCTWContrastIV,
  FemoralArteryFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_FemoralArteryFluoroscopicAngiogramAngioplastyWContrastIA,
  FemoralArteryFluoroscopicAngiogramRunoffWContrastIA: C80DocTypecodes_FemoralArteryFluoroscopicAngiogramRunoffWContrastIA,
  FemurAndTibiaXRayForLegLength: C80DocTypecodes_FemurAndTibiaXRayForLegLength,
  ThighMRI: C80DocTypecodes_ThighMRI,
  ThighMRIWAndWOContrastIV: C80DocTypecodes_ThighMRIWAndWOContrastIV,
  FemurXRay: C80DocTypecodes_FemurXRay,
  FingerMRI: C80DocTypecodes_FingerMRI,
  FingerXRay: C80DocTypecodes_FingerXRay,
  FootMRI: C80DocTypecodes_FootMRI,
  FootXRayStanding: C80DocTypecodes_FootXRayStanding,
  FootXRay: C80DocTypecodes_FootXRay,
  ForearmMRI: C80DocTypecodes_ForearmMRI,
  GallbladderUS: C80DocTypecodes_GallbladderUS,
  GallbladderXRayWContrastPO: C80DocTypecodes_GallbladderXRayWContrastPO,
  GallbladderXRay48HoursPostContrastPO: C80DocTypecodes_GallbladderXRay48HoursPostContrastPO,
  GastrointestineScanWTc99mTaggedRBCIV: C80DocTypecodes_GastrointestineScanWTc99mTaggedRBCIV,
  GastrointestineUpperFluoroscopySingleViewWContrastPO: C80DocTypecodes_GastrointestineUpperFluoroscopySingleViewWContrastPO,
  FluoroscopyGuidanceForPlacementOfDecompressionTubeInGastrointestine: C80DocTypecodes_FluoroscopyGuidanceForPlacementOfDecompressionTubeInGastrointestine,
  IlealConduitXRayLoopogram: C80DocTypecodes_IlealConduitXRayLoopogram,
  FluoroscopyGuidanceForTransjugularBiopsyOfLiverWContrastIV: C80DocTypecodes_FluoroscopyGuidanceForTransjugularBiopsyOfLiverWContrastIV,
  GroinUS: C80DocTypecodes_GroinUS,
  HandMRI: C80DocTypecodes_HandMRI,
  HandXRay2Views: C80DocTypecodes_HandXRay2Views,
  HandXRay3Views: C80DocTypecodes_HandXRay3Views,
  HandXRayArthritis: C80DocTypecodes_HandXRayArthritis,
  WristAndHandXRayBoneAge: C80DocTypecodes_WristAndHandXRayBoneAge,
  HeadCT: C80DocTypecodes_HeadCT,
  HeadCTWAndWOContrastIV: C80DocTypecodes_HeadCTWAndWOContrastIV,
  HeadCTWContrastIV: C80DocTypecodes_HeadCTWContrastIV,
  HeadCTCine: C80DocTypecodes_HeadCTCine,
  HeadCTCineWAndWOContrastIV: C80DocTypecodes_HeadCTCineWAndWOContrastIV,
  BrainScan: C80DocTypecodes_BrainScan,
  HeadUS: C80DocTypecodes_HeadUS,
  HeadUSDuringSurgery: C80DocTypecodes_HeadUSDuringSurgery,
  HeadVesselsUSDoppler: C80DocTypecodes_HeadVesselsUSDoppler,
  HeadCisternCTWContrastIT: C80DocTypecodes_HeadCisternCTWContrastIT,
  InternalAuditoryCanalAndPosteriorFossaMRI: C80DocTypecodes_InternalAuditoryCanalAndPosteriorFossaMRI,
  InternalAuditoryCanalAndPosteriorFossaMRIWAndWOContrastIV: C80DocTypecodes_InternalAuditoryCanalAndPosteriorFossaMRIWAndWOContrastIV,
  PetrousBoneXRay: C80DocTypecodes_PetrousBoneXRay,
  HeadSagittalSinusMRI: C80DocTypecodes_HeadSagittalSinusMRI,
  HeadSagittalSinusMRIAngiogramWContrastIV: C80DocTypecodes_HeadSagittalSinusMRIAngiogramWContrastIV,
  HeartMRI: C80DocTypecodes_HeartMRI,
  HeartScanAtRestAndWTl201IV: C80DocTypecodes_HeartScanAtRestAndWTl201IV,
  ParathyroidScanWTI201SubtractionTc99mIV: C80DocTypecodes_ParathyroidScanWTI201SubtractionTc99mIV,
  HeartFluoroscopyVideo: C80DocTypecodes_HeartFluoroscopyVideo,
  UnspecifiedBodyRegionCTWContrastIV: C80DocTypecodes_UnspecifiedBodyRegionCTWContrastIV,
  AdministrationOfVasodilatorIntoCatheterOfVein: C80DocTypecodes_AdministrationOfVasodilatorIntoCatheterOfVein,
  FluoroscopicAngiogramGuidanceForAtherectomyOfVeinWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForAtherectomyOfVeinWContrastIV,
  FluoroscopicAngiogramGuidanceForPlacementOfStentInVein: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInVein,
  CoronaryArteriesCTFast: C80DocTypecodes_CoronaryArteriesCTFast,
  HipUS: C80DocTypecodes_HipUS,
  HipXRaySingleView: C80DocTypecodes_HipXRaySingleView,
  HipXRay: C80DocTypecodes_HipXRay,
  HipFluoroscopyWContrastIntraarticular: C80DocTypecodes_HipFluoroscopyWContrastIntraarticular,
  HumerusXRay2Views: C80DocTypecodes_HumerusXRay2Views,
  IliacArteryFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_IliacArteryFluoroscopicAngiogramAngioplastyWContrastIA,
  InternalAuditoryCanalXRayTomograph: C80DocTypecodes_InternalAuditoryCanalXRayTomograph,
  CTGuidanceForInjectionOfJointSpace: C80DocTypecodes_CTGuidanceForInjectionOfJointSpace,
  JointScanWIn111Intrajoint: C80DocTypecodes_JointScanWIn111Intrajoint,
  FluoroscopyGuidanceForAspirationOfJointSpace: C80DocTypecodes_FluoroscopyGuidanceForAspirationOfJointSpace,
  USGuidanceForBiopsyOfKidney: C80DocTypecodes_USGuidanceForBiopsyOfKidney,
  KidneyBilateralScanWRadionuclideTransplantScan: C80DocTypecodes_KidneyBilateralScanWRadionuclideTransplantScan,
  KidneyBilateralScan: C80DocTypecodes_KidneyBilateralScan,
  KidneyBilateralXRay3ViewsSerialWAndWOContrastIV: C80DocTypecodes_KidneyBilateralXRay3ViewsSerialWAndWOContrastIV,
  FluoroscopyGuidanceForPlacementOfPercutaneousNephrostomyInKidneyBilateralWContrastViaTube: C80DocTypecodes_FluoroscopyGuidanceForPlacementOfPercutaneousNephrostomyInKidneyBilateralWContrastViaTube,
  KidneyBilateralFluoroscopyWContrastViaNephrostomyTube: C80DocTypecodes_KidneyBilateralFluoroscopyWContrastViaNephrostomyTube,
  FluoroscopyGuidanceForChangeOfPercutaneousNephrostomyTubeInKidneyBilateralWContrast: C80DocTypecodes_FluoroscopyGuidanceForChangeOfPercutaneousNephrostomyTubeInKidneyBilateralWContrast,
  FluoroscopyGuidanceForPlacementOfPercutaneousNephroureteralStentInKidneyBilateral: C80DocTypecodes_FluoroscopyGuidanceForPlacementOfPercutaneousNephroureteralStentInKidneyBilateral,
  KidneyBilateralFluoroscopyUrodynamics: C80DocTypecodes_KidneyBilateralFluoroscopyUrodynamics,
  KidneyBilateralXRayTomographWAndWOContrastIV: C80DocTypecodes_KidneyBilateralXRayTomographWAndWOContrastIV,
  KidneyBilateralXRayTomographWOContrastAnd10MPostContrastIV: C80DocTypecodes_KidneyBilateralXRayTomographWOContrastAnd10MPostContrastIV,
  KidneyBilateralXRayWContrastIV: C80DocTypecodes_KidneyBilateralXRayWContrastIV,
  KidneyBilateralXRayTomograph: C80DocTypecodes_KidneyBilateralXRayTomograph,
  KidneyBilateralXRayTomographWContrastIV: C80DocTypecodes_KidneyBilateralXRayTomographWContrastIV,
  AbdomenXRayAPAndAPLeftLateralDecubitusPortable: C80DocTypecodes_AbdomenXRayAPAndAPLeftLateralDecubitusPortable,
  AbdomenXRayAPAndLateralPortable: C80DocTypecodes_AbdomenXRayAPAndLateralPortable,
  AbdomenXRayAPAndLateral: C80DocTypecodes_AbdomenXRayAPAndLateral,
  AbdomenXRayAPSupineAndUprightPortable: C80DocTypecodes_AbdomenXRayAPSupineAndUprightPortable,
  AbdomenXRayAPAndAPLeftLateralDecubitus: C80DocTypecodes_AbdomenXRayAPAndAPLeftLateralDecubitus,
  AbdomenXRayAPAndObliqueProne: C80DocTypecodes_AbdomenXRayAPAndObliqueProne,
  AbdomenXRayAPSupineAndUpright: C80DocTypecodes_AbdomenXRayAPSupineAndUpright,
  AbdomenXRayAPSingleView: C80DocTypecodes_AbdomenXRayAPSingleView,
  KneeFluoroscopyWContrastIntraarticular: C80DocTypecodes_KneeFluoroscopyWContrastIntraarticular,
  KneeXRayMerchants: C80DocTypecodes_KneeXRayMerchants,
  KneeMRI: C80DocTypecodes_KneeMRI,
  KneeMRIWAndWOContrastIV: C80DocTypecodes_KneeMRIWAndWOContrastIV,
  KneeScan: C80DocTypecodes_KneeScan,
  KneeXRayAPAndLateralStanding: C80DocTypecodes_KneeXRayAPAndLateralStanding,
  KneeXRay2Views: C80DocTypecodes_KneeXRay2Views,
  KneeXRayAPSingleViewStanding: C80DocTypecodes_KneeXRayAPSingleViewStanding,
  KneeXRayAPAndPAStanding: C80DocTypecodes_KneeXRayAPAndPAStanding,
  KneeXRayStanding: C80DocTypecodes_KneeXRayStanding,
  CTGuidanceForAspirationOfLiver: C80DocTypecodes_CTGuidanceForAspirationOfLiver,
  CTGuidanceForBiopsyOfLiver: C80DocTypecodes_CTGuidanceForBiopsyOfLiver,
  CTGuidanceForCoreNeedleBiopsyOfLiver: C80DocTypecodes_CTGuidanceForCoreNeedleBiopsyOfLiver,
  LiverCT: C80DocTypecodes_LiverCT,
  LiverCTWContrastIV: C80DocTypecodes_LiverCTWContrastIV,
  USGuidanceForBiopsyOfLiver: C80DocTypecodes_USGuidanceForBiopsyOfLiver,
  LiverSPECTWTc99mIV: C80DocTypecodes_LiverSPECTWTc99mIV,
  LiverAndDiaphragmUS: C80DocTypecodes_LiverAndDiaphragmUS,
  LiverAndSpleenScanWTc99mCalciumColloidIV: C80DocTypecodes_LiverAndSpleenScanWTc99mCalciumColloidIV,
  LowerLegVesselsMRIAngiogramWContrastIV: C80DocTypecodes_LowerLegVesselsMRIAngiogramWContrastIV,
  LowerLegMRI: C80DocTypecodes_LowerLegMRI,
  CTGuidanceForAspirationOfLung: C80DocTypecodes_CTGuidanceForAspirationOfLung,
  CTGuidanceForBiopsyOfLung: C80DocTypecodes_CTGuidanceForBiopsyOfLung,
  LungScanPortable: C80DocTypecodes_LungScanPortable,
  LungXRayWContrastIntrabronchial: C80DocTypecodes_LungXRayWContrastIntrabronchial,
  LymphaticsScanWRadionuclideIntraLymphatic: C80DocTypecodes_LymphaticsScanWRadionuclideIntraLymphatic,
  LymphaticsFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_LymphaticsFluoroscopyWContrastIntraLymphatic,
  MandibleXRayPanorex: C80DocTypecodes_MandibleXRayPanorex,
  MandibleXRay: C80DocTypecodes_MandibleXRay,
  MastoidXRay: C80DocTypecodes_MastoidXRay,
  MeckelsDiverticulumScanWTc99mM04IV: C80DocTypecodes_MeckelsDiverticulumScanWTc99mM04IV,
  MesentericArteryFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_MesentericArteryFluoroscopicAngiogramAngioplastyWContrastIA,
  MesentericArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_MesentericArteryFluoroscopicAngiogramWContrastIA,
  NasalBonesXRay: C80DocTypecodes_NasalBonesXRay,
  NasopharynxAndNeckCT: C80DocTypecodes_NasopharynxAndNeckCT,
  NasopharynxAndNeckCTWContrastIV: C80DocTypecodes_NasopharynxAndNeckCTWContrastIV,
  CTGuidanceForAspirationOfNeck: C80DocTypecodes_CTGuidanceForAspirationOfNeck,
  CTGuidanceForBiopsyOfNeck: C80DocTypecodes_CTGuidanceForBiopsyOfNeck,
  NeckMRI: C80DocTypecodes_NeckMRI,
  NeckMRIWAndWOContrastIV: C80DocTypecodes_NeckMRIWAndWOContrastIV,
  NeckMRIWContrastIV: C80DocTypecodes_NeckMRIWContrastIV,
  NeckUS: C80DocTypecodes_NeckUS,
  NeckXRayLateral: C80DocTypecodes_NeckXRayLateral,
  NeckVesselsMRIAngiogramWContrastIV: C80DocTypecodes_NeckVesselsMRIAngiogramWContrastIV,
  NeckFluoroscopyWContrastIntraLarynx: C80DocTypecodes_NeckFluoroscopyWContrastIntraLarynx,
  OpticForamenXRay: C80DocTypecodes_OpticForamenXRay,
  OrbitBilateralCT: C80DocTypecodes_OrbitBilateralCT,
  OrbitBilateralCTWAndWOContrastIV: C80DocTypecodes_OrbitBilateralCTWAndWOContrastIV,
  OrbitBilateralCTWContrastIV: C80DocTypecodes_OrbitBilateralCTWContrastIV,
  OrbitBilateralMRIWAndWOContrastIV: C80DocTypecodes_OrbitBilateralMRIWAndWOContrastIV,
  OrbitBilateralMRIWContrastIV: C80DocTypecodes_OrbitBilateralMRIWContrastIV,
  EyePlusOrbitBilateralUS: C80DocTypecodes_EyePlusOrbitBilateralUS,
  OrbitBilateralXRay: C80DocTypecodes_OrbitBilateralXRay,
  OropharynxFluoroscopyVideo: C80DocTypecodes_OropharynxFluoroscopyVideo,
  CTGuidanceForAspirationOfPancreas: C80DocTypecodes_CTGuidanceForAspirationOfPancreas,
  PancreasCT: C80DocTypecodes_PancreasCT,
  PancreasCTWContrastIV: C80DocTypecodes_PancreasCTWContrastIV,
  PancreasUS: C80DocTypecodes_PancreasUS,
  PancreaticArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_PancreaticArteryFluoroscopicAngiogramWContrastIA,
  PatellaXRay2Views: C80DocTypecodes_PatellaXRay2Views,
  IliacArteryInternalFluoroscopicAngiogramWContrastIA: C80DocTypecodes_IliacArteryInternalFluoroscopicAngiogramWContrastIA,
  CTGuidanceForAspirationOfPelvis: C80DocTypecodes_CTGuidanceForAspirationOfPelvis,
  CTGuidanceForBiopsyOfPelvis: C80DocTypecodes_CTGuidanceForBiopsyOfPelvis,
  PelvisCT: C80DocTypecodes_PelvisCT,
  PelvisCTWContrastIV: C80DocTypecodes_PelvisCTWContrastIV,
  PelvisMRI: C80DocTypecodes_PelvisMRI,
  USGuidanceForDrainageOfPelvis: C80DocTypecodes_USGuidanceForDrainageOfPelvis,
  PelvisUS: C80DocTypecodes_PelvisUS,
  PelvisVesselsUSDoppler: C80DocTypecodes_PelvisVesselsUSDoppler,
  PelvisXRayPelvimetry: C80DocTypecodes_PelvisXRayPelvimetry,
  PelvisAndHipMRI: C80DocTypecodes_PelvisAndHipMRI,
  PelvisVesselsMRIAngiogramWContrastIV: C80DocTypecodes_PelvisVesselsMRIAngiogramWContrastIV,
  PeripheralArteriesFluoroscopicAngiogramWContrastIA: C80DocTypecodes_PeripheralArteriesFluoroscopicAngiogramWContrastIA,
  PeripheralVesselUSDopplerPeripheralPlane: C80DocTypecodes_PeripheralVesselUSDopplerPeripheralPlane,
  PeritoneovenousShuntScanForPatencyWTc99mDTPAIT: C80DocTypecodes_PeritoneovenousShuntScanForPatencyWTc99mDTPAIT,
  PetrousBoneCT: C80DocTypecodes_PetrousBoneCT,
  PetrousBoneCTWContrastIV: C80DocTypecodes_PetrousBoneCTWContrastIV,
  PituitaryAndSellaTurcicaMRIWAndWOContrastIV: C80DocTypecodes_PituitaryAndSellaTurcicaMRIWAndWOContrastIV,
  PituitaryAndSellaTurcicaMRI: C80DocTypecodes_PituitaryAndSellaTurcicaMRI,
  PoplitealSpaceUS: C80DocTypecodes_PoplitealSpaceUS,
  PoplitealArteryFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIA: C80DocTypecodes_PoplitealArteryFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIA,
  USGuidanceForBiopsyOfProstate: C80DocTypecodes_USGuidanceForBiopsyOfProstate,
  ProstateUS: C80DocTypecodes_ProstateUS,
  USGuidanceForRepairOfPseudoaneurysmAVFistula: C80DocTypecodes_USGuidanceForRepairOfPseudoaneurysmAVFistula,
  PulmonaryArteryFluoroscopicAngiogramEmbolectomyWContrastIA: C80DocTypecodes_PulmonaryArteryFluoroscopicAngiogramEmbolectomyWContrastIA,
  PulmonarySystemScanVentilationAndPerfusionWXe133InhaledAndWTc99mMAAIV: C80DocTypecodes_PulmonarySystemScanVentilationAndPerfusionWXe133InhaledAndWTc99mMAAIV,
  PylorusUSForPyloricStenosis: C80DocTypecodes_PylorusUSForPyloricStenosis,
  RadiusAndUlnaXRay: C80DocTypecodes_RadiusAndUlnaXRay,
  RectumUS: C80DocTypecodes_RectumUS,
  RectumFluoroscopyPostContrastPRDuringDefecation: C80DocTypecodes_RectumFluoroscopyPostContrastPRDuringDefecation,
  RectumAndUrinaryBladderFluoroscopyWContrastPRAndIntraBladderDuringDefecationAndVoiding: C80DocTypecodes_RectumAndUrinaryBladderFluoroscopyWContrastPRAndIntraBladderDuringDefecationAndVoiding,
  USGuidanceForDrainageOfKidney: C80DocTypecodes_USGuidanceForDrainageOfKidney,
  RibsXRay: C80DocTypecodes_RibsXRay,
  SacroiliacJointXRay: C80DocTypecodes_SacroiliacJointXRay,
  CTGuidanceForInjectionOfSacroiliacJoint: C80DocTypecodes_CTGuidanceForInjectionOfSacroiliacJoint,
  SalivaryGlandFluoroscopyWContrastIntraSalivaryDuct: C80DocTypecodes_SalivaryGlandFluoroscopyWContrastIntraSalivaryDuct,
  ScapulaXRay: C80DocTypecodes_ScapulaXRay,
  PituitaryAndSellaTurcicaCTWAndWOContrastIV: C80DocTypecodes_PituitaryAndSellaTurcicaCTWAndWOContrastIV,
  ShoulderMRI: C80DocTypecodes_ShoulderMRI,
  ShoulderMRIWAndWOContrastIV: C80DocTypecodes_ShoulderMRIWAndWOContrastIV,
  ShoulderUS: C80DocTypecodes_ShoulderUS,
  ShoulderXRay3Views: C80DocTypecodes_ShoulderXRay3Views,
  ShoulderXRay: C80DocTypecodes_ShoulderXRay,
  ShoulderFluoroscopyWContrastIntraarticular: C80DocTypecodes_ShoulderFluoroscopyWContrastIntraarticular,
  ShuntFluoroscopy: C80DocTypecodes_ShuntFluoroscopy,
  SinusTractFluoroscopyWContrastIntraSinusTract: C80DocTypecodes_SinusTractFluoroscopyWContrastIntraSinusTract,
  SinusesCTLimited: C80DocTypecodes_SinusesCTLimited,
  SinusesMRI: C80DocTypecodes_SinusesMRI,
  SinusesMRIWContrastIV: C80DocTypecodes_SinusesMRIWContrastIV,
  SinusesXRay: C80DocTypecodes_SinusesXRay,
  SkullXRaySingleView: C80DocTypecodes_SkullXRaySingleView,
  SkullXRay3Views: C80DocTypecodes_SkullXRay3Views,
  SkullXRayAPAndLateral: C80DocTypecodes_SkullXRayAPAndLateral,
  SkullXRayLateral: C80DocTypecodes_SkullXRayLateral,
  SkullXRayWaters: C80DocTypecodes_SkullXRayWaters,
  SkullXRay5Views: C80DocTypecodes_SkullXRay5Views,
  SmallBowelFluoroscopyViewsEnteroclysisWContrastPOViaDuodenalIntubation: C80DocTypecodes_SmallBowelFluoroscopyViewsEnteroclysisWContrastPOViaDuodenalIntubation,
  SmallBowelFluoroscopyWContrastPO: C80DocTypecodes_SmallBowelFluoroscopyWContrastPO,
  SpinalArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_SpinalArteryFluoroscopicAngiogramWContrastIA,
  SpineUS: C80DocTypecodes_SpineUS,
  SpineFluoroscopyWContrastIntradisc: C80DocTypecodes_SpineFluoroscopyWContrastIntradisc,
  SpineXRayAPAndLateral: C80DocTypecodes_SpineXRayAPAndLateral,
  SpineThoracicAndLumbarXRayScoliosisWFlexionAndWExtension: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisWFlexionAndWExtension,
  SpineThoracicAndLumbarXRayScoliosis: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosis,
  FluoroscopyGuidanceForInjectionOfSpineFacetJoint: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineFacetJoint,
  SpineCervicalCT: C80DocTypecodes_SpineCervicalCT,
  SpineCervicalCTWContrastIV: C80DocTypecodes_SpineCervicalCTWContrastIV,
  SpineCervicalCTWContrastIT: C80DocTypecodes_SpineCervicalCTWContrastIT,
  SpineCervicalMRI: C80DocTypecodes_SpineCervicalMRI,
  SpineCervicalMRIWAnesthesia: C80DocTypecodes_SpineCervicalMRIWAnesthesia,
  SpineCervicalMRIWAndWOContrastIV: C80DocTypecodes_SpineCervicalMRIWAndWOContrastIV,
  SpineCervicalMRIWContrastIV: C80DocTypecodes_SpineCervicalMRIWContrastIV,
  SpineCervicalXRay5Views: C80DocTypecodes_SpineCervicalXRay5Views,
  SpineCervicalXRaySingleView: C80DocTypecodes_SpineCervicalXRaySingleView,
  SpineCervicalXRay3Views: C80DocTypecodes_SpineCervicalXRay3Views,
  SpineCervicalXRayAPAndLateral: C80DocTypecodes_SpineCervicalXRayAPAndLateral,
  SpineCervicalXRayLateral: C80DocTypecodes_SpineCervicalXRayLateral,
  SpineCervicalXRaySwimmers: C80DocTypecodes_SpineCervicalXRaySwimmers,
  SpineCervicalXRayWFlexionAndWExtension: C80DocTypecodes_SpineCervicalXRayWFlexionAndWExtension,
  SpineCervicalXRay: C80DocTypecodes_SpineCervicalXRay,
  SpineCervicalFluoroscopyWContrastIT: C80DocTypecodes_SpineCervicalFluoroscopyWContrastIT,
  SpineCervicalOdontoidAndCervicalAxisXRayAPSingleView: C80DocTypecodes_SpineCervicalOdontoidAndCervicalAxisXRayAPSingleView,
  SpineLumbarCT: C80DocTypecodes_SpineLumbarCT,
  SpineLumbarCTWContrastIV: C80DocTypecodes_SpineLumbarCTWContrastIV,
  SpineLumbarCTWContrastIT: C80DocTypecodes_SpineLumbarCTWContrastIT,
  SpineLumbarMRIWAndWOContrastIV: C80DocTypecodes_SpineLumbarMRIWAndWOContrastIV,
  SpineLumbarMRI: C80DocTypecodes_SpineLumbarMRI,
  SpineLumbarXRayLateral: C80DocTypecodes_SpineLumbarXRayLateral,
  SpineLumbarXRayAPAndLateral: C80DocTypecodes_SpineLumbarXRayAPAndLateral,
  SpineLumbarXRayWFlexionAndWExtension: C80DocTypecodes_SpineLumbarXRayWFlexionAndWExtension,
  SpineLumbarXRay: C80DocTypecodes_SpineLumbarXRay,
  FluoroscopyGuidanceForAspirationOfSpineLumbarSpace: C80DocTypecodes_FluoroscopyGuidanceForAspirationOfSpineLumbarSpace,
  SpineLumbarFluoroscopyWContrastIT: C80DocTypecodes_SpineLumbarFluoroscopyWContrastIT,
  SpineLumbarAndSacroiliacJointBilateralXRay: C80DocTypecodes_SpineLumbarAndSacroiliacJointBilateralXRay,
  SpineLumbarMRIWAnesthesia: C80DocTypecodes_SpineLumbarMRIWAnesthesia,
  SpineThoracicCT: C80DocTypecodes_SpineThoracicCT,
  SpineThoracicCTWContrastIV: C80DocTypecodes_SpineThoracicCTWContrastIV,
  SpineThoracicMRI: C80DocTypecodes_SpineThoracicMRI,
  SpineThoracicMRIWAndWOContrastIV: C80DocTypecodes_SpineThoracicMRIWAndWOContrastIV,
  SpineThoracicMRIWContrastIV: C80DocTypecodes_SpineThoracicMRIWContrastIV,
  SpineThoracicXRay: C80DocTypecodes_SpineThoracicXRay,
  SpineThoracicAndLumbarXRay2Views: C80DocTypecodes_SpineThoracicAndLumbarXRay2Views,
  SpineThoracicFluoroscopyWContrastIT: C80DocTypecodes_SpineThoracicFluoroscopyWContrastIT,
  CTGuidanceForBiopsyOfSpine: C80DocTypecodes_CTGuidanceForBiopsyOfSpine,
  SpineCTWContrastIV: C80DocTypecodes_SpineCTWContrastIV,
  SpleenCT: C80DocTypecodes_SpleenCT,
  SpleenCTWAndWOContrastIV: C80DocTypecodes_SpleenCTWAndWOContrastIV,
  SpleenUS: C80DocTypecodes_SpleenUS,
  SplenicVeinAndPortalVeinFluoroscopicAngiogramWContrastIA: C80DocTypecodes_SplenicVeinAndPortalVeinFluoroscopicAngiogramWContrastIA,
  SplenicArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_SplenicArteryFluoroscopicAngiogramWContrastIA,
  SternoclavicularJointsXRay: C80DocTypecodes_SternoclavicularJointsXRay,
  SternumXRay: C80DocTypecodes_SternumXRay,
  FluoroscopyGuidanceForPlacementOfTubeInStomach: C80DocTypecodes_FluoroscopyGuidanceForPlacementOfTubeInStomach,
  FluoroscopyGuidanceForReplacementOfPercutaneousGastrostomyInStomach: C80DocTypecodes_FluoroscopyGuidanceForReplacementOfPercutaneousGastrostomyInStomach,
  StomachScanForGastricEmptyingSolidPhaseWTc99mSCPO: C80DocTypecodes_StomachScanForGastricEmptyingSolidPhaseWTc99mSCPO,
  PlacementCheckOfGastrostomyTubeWContrastViaGITube: C80DocTypecodes_PlacementCheckOfGastrostomyTubeWContrastViaGITube,
  TemporomandibularJointMRI: C80DocTypecodes_TemporomandibularJointMRI,
  TemporomandibularJointXRay: C80DocTypecodes_TemporomandibularJointXRay,
  ScrotumAndTesticleScanWTc99mPertechnetateIV: C80DocTypecodes_ScrotumAndTesticleScanWTc99mPertechnetateIV,
  ScrotumAndTesticleUS: C80DocTypecodes_ScrotumAndTesticleUS,
  ThighVesselsMRIAngiogramWContrastIV: C80DocTypecodes_ThighVesselsMRIAngiogramWContrastIV,
  ThreeVesselsFluoroscopicAngiogramWContrast: C80DocTypecodes_ThreeVesselsFluoroscopicAngiogramWContrast,
  ThumbXRay: C80DocTypecodes_ThumbXRay,
  ThyroidScanWI131IV: C80DocTypecodes_ThyroidScanWI131IV,
  ThyroidScanAndUptakeWI131IV: C80DocTypecodes_ThyroidScanAndUptakeWI131IV,
  USGuidanceForBiopsyOfThyroid: C80DocTypecodes_USGuidanceForBiopsyOfThyroid,
  ThyroidUS: C80DocTypecodes_ThyroidUS,
  TibiaAndFibulaXRay: C80DocTypecodes_TibiaAndFibulaXRay,
  TibialArteryFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_TibialArteryFluoroscopicAngiogramAngioplastyWContrastIA,
  ToesXRay: C80DocTypecodes_ToesXRay,
  TwoVesselsFluoroscopicAngiogramWContrast: C80DocTypecodes_TwoVesselsFluoroscopicAngiogramWContrast,
  UpperGITractReplacementOfPercutaneousGastrojejunostomy: C80DocTypecodes_UpperGITractReplacementOfPercutaneousGastrojejunostomy,
  UrethraFluoroscopyWContrastIntraUrethra: C80DocTypecodes_UrethraFluoroscopyWContrastIntraUrethra,
  UrinaryBladderAndUrethraFluoroscopyWContrastIntraBladder: C80DocTypecodes_UrinaryBladderAndUrethraFluoroscopyWContrastIntraBladder,
  UrinaryBladderScan: C80DocTypecodes_UrinaryBladderScan,
  UrinaryBladderUS: C80DocTypecodes_UrinaryBladderUS,
  UrinaryBladderAndUrethraFluoroscopyWContrastRetrogradeViaUrethra: C80DocTypecodes_UrinaryBladderAndUrethraFluoroscopyWContrastRetrogradeViaUrethra,
  UterusAndFallopianTubesFluoroscopyWContrastIntrauterine: C80DocTypecodes_UterusAndFallopianTubesFluoroscopyWContrastIntrauterine,
  VeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_VeinFluoroscopicAngiogramWContrastIV,
  FluoroscopicAngiogramGuidanceForPlacementOfLongtermPeripheralCatheterInCentralVein: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfLongtermPeripheralCatheterInCentralVein,
  VenaCavaFluoroscopicAngiogramWContrastIV: C80DocTypecodes_VenaCavaFluoroscopicAngiogramWContrastIV,
  FluoroscopicAngiogramGuidanceForPlacementOfIVCFilterInInferiorVenaCavaWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfIVCFilterInInferiorVenaCavaWContrastIV,
  GuidanceForPlacementOfLargeBoreCatheterIntoVesselInCentralVein: C80DocTypecodes_GuidanceForPlacementOfLargeBoreCatheterIntoVesselInCentralVein,
  FluoroscopicAngiogramGuidanceForPlacementOfCatheterForAdminstrationOfThrombolyticInVessel: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterForAdminstrationOfThrombolyticInVessel,
  FluoroscopicAngiogramGuidanceForPlacementOfCatheterForVasoconstrictorInfusionInVessels: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterForVasoconstrictorInfusionInVessels,
  AbdominalArteriesFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AbdominalArteriesFluoroscopicAngiogramWContrastIA,
  BoneScan: C80DocTypecodes_BoneScan,
  BoneScanWIn111TaggedWBCIV: C80DocTypecodes_BoneScanWIn111TaggedWBCIV,
  WristMRI: C80DocTypecodes_WristMRI,
  WristFluoroscopyWContrastIntraarticular: C80DocTypecodes_WristFluoroscopyWContrastIntraarticular,
  WristMRIWAndWOContrastIV: C80DocTypecodes_WristMRIWAndWOContrastIV,
  WristUS: C80DocTypecodes_WristUS,
  UnspecifiedBodyRegionCourtesyConsultation: C80DocTypecodes_UnspecifiedBodyRegionCourtesyConsultation,
  UnspecifiedBodyRegionCTLimited: C80DocTypecodes_UnspecifiedBodyRegionCTLimited,
  UnspecifiedBodyRegionCT3D: C80DocTypecodes_UnspecifiedBodyRegionCT3D,
  CTGuidanceForAspirationOrBiopsyOfUnspecifiedBodyRegionWContrastIV: C80DocTypecodes_CTGuidanceForAspirationOrBiopsyOfUnspecifiedBodyRegionWContrastIV,
  CTGuidanceForAspirationOrBiopsyOfUnspecifiedBodyRegion: C80DocTypecodes_CTGuidanceForAspirationOrBiopsyOfUnspecifiedBodyRegion,
  CTGuidanceForAspirationOfUnspecifiedBodyRegion: C80DocTypecodes_CTGuidanceForAspirationOfUnspecifiedBodyRegion,
  CTGuidanceForBiopsyOfUnspecifiedBodyRegion: C80DocTypecodes_CTGuidanceForBiopsyOfUnspecifiedBodyRegion,
  UnspecifiedBodyRegionCT: C80DocTypecodes_UnspecifiedBodyRegionCT,
  UnspecifiedBodyRegionCTWAnesthesia: C80DocTypecodes_UnspecifiedBodyRegionCTWAnesthesia,
  UnspecifiedBodyRegionCTWConsciousSedation: C80DocTypecodes_UnspecifiedBodyRegionCTWConsciousSedation,
  UnspecifiedBodyRegionCT3DSagittalAndCoronalDisarticulation: C80DocTypecodes_UnspecifiedBodyRegionCT3DSagittalAndCoronalDisarticulation,
  UnspecifiedBodyRegionCTMultisectionalSagittal: C80DocTypecodes_UnspecifiedBodyRegionCTMultisectionalSagittal,
  UnspecifiedBodyRegionCTSagittalAndCoronal: C80DocTypecodes_UnspecifiedBodyRegionCTSagittalAndCoronal,
  CTGuidanceForRadiosurgeryOfUnspecifiedBodyRegion: C80DocTypecodes_CTGuidanceForRadiosurgeryOfUnspecifiedBodyRegion,
  CTGuidanceForRadiosurgeryOfUnspecifiedBodyRegionWContrastIV: C80DocTypecodes_CTGuidanceForRadiosurgeryOfUnspecifiedBodyRegionWContrastIV,
  UnspecifiedBodyRegionMRIAdditionalSequence: C80DocTypecodes_UnspecifiedBodyRegionMRIAdditionalSequence,
  UnspecifiedBodyRegionMRI: C80DocTypecodes_UnspecifiedBodyRegionMRI,
  UnspecifiedBodyRegionMRIWConsciousSedation: C80DocTypecodes_UnspecifiedBodyRegionMRIWConsciousSedation,
  UnspecifiedBodyRegionMRIAngiogramWContrastIV: C80DocTypecodes_UnspecifiedBodyRegionMRIAngiogramWContrastIV,
  USGuidanceForBiopsyOfUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForBiopsyOfUnspecifiedBodyRegion,
  UnspecifiedBodyRegionUSNoCharge: C80DocTypecodes_UnspecifiedBodyRegionUSNoCharge,
  UnspecifiedBodyRegionUS: C80DocTypecodes_UnspecifiedBodyRegionUS,
  UnspecifiedBodyRegionXRayComparisonView: C80DocTypecodes_UnspecifiedBodyRegionXRayComparisonView,
  VesselFluoroscopicAngiogramSingleViewWContrastIA: C80DocTypecodes_VesselFluoroscopicAngiogramSingleViewWContrastIA,
  VesselFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_VesselFluoroscopicAngiogramAngioplastyWContrastIA,
  UnspecifiedBodyRegionFluoroscopy15Minutes: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy15Minutes,
  UnspecifiedBodyRegionFluoroscopy30Minutes: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy30Minutes,
  UnspecifiedBodyRegionFluoroscopy45Minutes: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy45Minutes,
  UnspecifiedBodyRegionFluoroscopy1Hour: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy1Hour,
  FluoroscopyGuidanceForBiopsyOfUnspecifiedBodyRegion: C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfUnspecifiedBodyRegion,
  UnspecifiedBodyRegionFluoroscopyDuringSurgery: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyDuringSurgery,
  UnspecifiedBodyRegionXRayTomograph: C80DocTypecodes_UnspecifiedBodyRegionXRayTomograph,
  GuidanceForPlacementOfInfusionPortInUnspecifiedBodyRegion: C80DocTypecodes_GuidanceForPlacementOfInfusionPortInUnspecifiedBodyRegion,
  VesselFluoroscopicAngiogramRemovalOfForeignBodyFromVascularSpace: C80DocTypecodes_VesselFluoroscopicAngiogramRemovalOfForeignBodyFromVascularSpace,
  ZygomaticArchXRay: C80DocTypecodes_ZygomaticArchXRay,
  HepaticArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_HepaticArteryFluoroscopicAngiogramWContrastIA,
  FluoroscopicAngiogramGuidanceForPlacementOfCatheterInHepaticArteryWContrastIA: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInHepaticArteryWContrastIA,
  FluoroscopyGuidanceForPlacementOfStentInIntrahepaticPortalSystem: C80DocTypecodes_FluoroscopyGuidanceForPlacementOfStentInIntrahepaticPortalSystem,
  KidneyArteriesFluoroscopicAngiogramWContrastIA: C80DocTypecodes_KidneyArteriesFluoroscopicAngiogramWContrastIA,
  RenalVeinBilateralFluoroscopicAngiogramWContrastIVAndWReninSampling: C80DocTypecodes_RenalVeinBilateralFluoroscopicAngiogramWContrastIVAndWReninSampling,
  RenalVesselFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_RenalVesselFluoroscopicAngiogramAngioplastyWContrastIA,
  VAL11DeoxycorticosteroneMolesVolumeInSerumOrPlasma: C80DocTypecodes_VAL11DeoxycorticosteroneMolesVolumeInSerumOrPlasma,
  VeinBilateralFluoroscopicAngiogramWContrastIV: C80DocTypecodes_VeinBilateralFluoroscopicAngiogramWContrastIV,
  VeinLeftFluoroscopicAngiogramWContrastIV: C80DocTypecodes_VeinLeftFluoroscopicAngiogramWContrastIV,
  VeinRightFluoroscopicAngiogramWContrastIV: C80DocTypecodes_VeinRightFluoroscopicAngiogramWContrastIV,
  SalivaryGlandBilateralFluoroscopyWContrastIntraSalivaryDuct: C80DocTypecodes_SalivaryGlandBilateralFluoroscopyWContrastIntraSalivaryDuct,
  SalivaryGlandLeftFluoroscopyWContrastIntraSalivaryDuct: C80DocTypecodes_SalivaryGlandLeftFluoroscopyWContrastIntraSalivaryDuct,
  SalivaryGlandRightFluoroscopyWContrastIntraSalivaryDuct: C80DocTypecodes_SalivaryGlandRightFluoroscopyWContrastIntraSalivaryDuct,
  HipBilateralFluoroscopyWContrastIntraarticular: C80DocTypecodes_HipBilateralFluoroscopyWContrastIntraarticular,
  HipLeftFluoroscopyWContrastIntraarticular: C80DocTypecodes_HipLeftFluoroscopyWContrastIntraarticular,
  HipRightFluoroscopyWContrastIntraarticular: C80DocTypecodes_HipRightFluoroscopyWContrastIntraarticular,
  KneeBilateralFluoroscopyWContrastIntraarticular: C80DocTypecodes_KneeBilateralFluoroscopyWContrastIntraarticular,
  KneeLeftFluoroscopyWContrastIntraarticular: C80DocTypecodes_KneeLeftFluoroscopyWContrastIntraarticular,
  KneeRightFluoroscopyWContrastIntraarticular: C80DocTypecodes_KneeRightFluoroscopyWContrastIntraarticular,
  ShoulderBilateralFluoroscopyWContrastIntraarticular: C80DocTypecodes_ShoulderBilateralFluoroscopyWContrastIntraarticular,
  ShoulderLeftFluoroscopyWContrastIntraarticular: C80DocTypecodes_ShoulderLeftFluoroscopyWContrastIntraarticular,
  ShoulderRightFluoroscopyWContrastIntraarticular: C80DocTypecodes_ShoulderRightFluoroscopyWContrastIntraarticular,
  CarotidArteryBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryBilateralFluoroscopicAngiogramWContrastIA,
  CarotidArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryLeftFluoroscopicAngiogramWContrastIA,
  CarotidArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryRightFluoroscopicAngiogramWContrastIA,
  SpinalArteryBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_SpinalArteryBilateralFluoroscopicAngiogramWContrastIA,
  SpinalArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_SpinalArteryLeftFluoroscopicAngiogramWContrastIA,
  SpinalArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_SpinalArteryRightFluoroscopicAngiogramWContrastIA,
  KneeBilateralXRayStanding: C80DocTypecodes_KneeBilateralXRayStanding,
  KneeLeftXRayStanding: C80DocTypecodes_KneeLeftXRayStanding,
  KneeRightXRayStanding: C80DocTypecodes_KneeRightXRayStanding,
  KneeBilateralScan: C80DocTypecodes_KneeBilateralScan,
  KneeLeftScan: C80DocTypecodes_KneeLeftScan,
  KneeRightScan: C80DocTypecodes_KneeRightScan,
  ScrotumAndTesticleBilateralScanWTc99mPertechnetateIV: C80DocTypecodes_ScrotumAndTesticleBilateralScanWTc99mPertechnetateIV,
  ScrotumAndTesticleLeftScanWTc99mPertechnetateIV: C80DocTypecodes_ScrotumAndTesticleLeftScanWTc99mPertechnetateIV,
  ScrotumAndTesticleRightScanWTc99mPertechnetateIV: C80DocTypecodes_ScrotumAndTesticleRightScanWTc99mPertechnetateIV,
  FootBilateralXRayStanding: C80DocTypecodes_FootBilateralXRayStanding,
  FootLeftXRayStanding: C80DocTypecodes_FootLeftXRayStanding,
  FootRightXRayStanding: C80DocTypecodes_FootRightXRayStanding,
  AnkleBilateralXRay: C80DocTypecodes_AnkleBilateralXRay,
  AnkleLeftXRay: C80DocTypecodes_AnkleLeftXRay,
  AnkleRightXRay: C80DocTypecodes_AnkleRightXRay,
  CalcaneusBilateralXRay: C80DocTypecodes_CalcaneusBilateralXRay,
  CalcaneusLeftXRay: C80DocTypecodes_CalcaneusLeftXRay,
  CalcaneusRightXRay: C80DocTypecodes_CalcaneusRightXRay,
  ClavicleBilateralXRay: C80DocTypecodes_ClavicleBilateralXRay,
  ClavicleLeftXRay: C80DocTypecodes_ClavicleLeftXRay,
  ClavicleRightXRay: C80DocTypecodes_ClavicleRightXRay,
  ElbowBilateralXRay: C80DocTypecodes_ElbowBilateralXRay,
  ElbowLeftXRay: C80DocTypecodes_ElbowLeftXRay,
  ElbowRightXRay: C80DocTypecodes_ElbowRightXRay,
  LowerExtremityBilateralXRay: C80DocTypecodes_LowerExtremityBilateralXRay,
  LowerExtremityLeftXRay: C80DocTypecodes_LowerExtremityLeftXRay,
  LowerExtremityRightXRay: C80DocTypecodes_LowerExtremityRightXRay,
  UpperExtremityBilateralXRay: C80DocTypecodes_UpperExtremityBilateralXRay,
  UpperExtremityLeftXRay: C80DocTypecodes_UpperExtremityLeftXRay,
  UpperExtremityRightXRay: C80DocTypecodes_UpperExtremityRightXRay,
  FemurBilateralXRay: C80DocTypecodes_FemurBilateralXRay,
  FemurLeftXRay: C80DocTypecodes_FemurLeftXRay,
  FemurRightXRay: C80DocTypecodes_FemurRightXRay,
  FingerBilateralXRay: C80DocTypecodes_FingerBilateralXRay,
  FingerLeftXRay: C80DocTypecodes_FingerLeftXRay,
  FingerRightXRay: C80DocTypecodes_FingerRightXRay,
  FootBilateralXRay: C80DocTypecodes_FootBilateralXRay,
  FootLeftXRay: C80DocTypecodes_FootLeftXRay,
  FootRightXRay: C80DocTypecodes_FootRightXRay,
  HipBilateralXRay: C80DocTypecodes_HipBilateralXRay,
  HipLeftXRay: C80DocTypecodes_HipLeftXRay,
  HipRightXRay: C80DocTypecodes_HipRightXRay,
  AcetabulumBilateralXRay: C80DocTypecodes_AcetabulumBilateralXRay,
  AcetabulumLeftXRay: C80DocTypecodes_AcetabulumLeftXRay,
  AcetabulumRightXRay: C80DocTypecodes_AcetabulumRightXRay,
  AcromioclavicularJointBilateralXRay: C80DocTypecodes_AcromioclavicularJointBilateralXRay,
  AcromioclavicularJointLeftXRay: C80DocTypecodes_AcromioclavicularJointLeftXRay,
  AcromioclavicularJointRightXRay: C80DocTypecodes_AcromioclavicularJointRightXRay,
  MastoidBilateralXRay: C80DocTypecodes_MastoidBilateralXRay,
  MastoidLeftXRay: C80DocTypecodes_MastoidLeftXRay,
  MastoidRightXRay: C80DocTypecodes_MastoidRightXRay,
  OpticForamenBilateralXRay: C80DocTypecodes_OpticForamenBilateralXRay,
  OpticForamenLeftXRay: C80DocTypecodes_OpticForamenLeftXRay,
  OpticForamenRightXRay: C80DocTypecodes_OpticForamenRightXRay,
  RadiusBilateralAndUlnaBilateralXRay: C80DocTypecodes_RadiusBilateralAndUlnaBilateralXRay,
  RadiusLeftAndUlnaLeftXRay: C80DocTypecodes_RadiusLeftAndUlnaLeftXRay,
  RadiusRightAndUlnaRightXRay: C80DocTypecodes_RadiusRightAndUlnaRightXRay,
  RibsBilateralXRay: C80DocTypecodes_RibsBilateralXRay,
  RibsLeftXRay: C80DocTypecodes_RibsLeftXRay,
  RibsRightXRay: C80DocTypecodes_RibsRightXRay,
  ScapulaBilateralXRay: C80DocTypecodes_ScapulaBilateralXRay,
  ScapulaLeftXRay: C80DocTypecodes_ScapulaLeftXRay,
  ScapulaRightXRay: C80DocTypecodes_ScapulaRightXRay,
  ShoulderBilateralXRay: C80DocTypecodes_ShoulderBilateralXRay,
  ShoulderLeftXRay: C80DocTypecodes_ShoulderLeftXRay,
  ShoulderRightXRay: C80DocTypecodes_ShoulderRightXRay,
  ThumbBilateralXRay: C80DocTypecodes_ThumbBilateralXRay,
  ThumbLeftXRay: C80DocTypecodes_ThumbLeftXRay,
  ThumbRightXRay: C80DocTypecodes_ThumbRightXRay,
  TibiaBilateralAndFibulaBilateralXRay: C80DocTypecodes_TibiaBilateralAndFibulaBilateralXRay,
  TibiaLeftAndFibulaLeftXRay: C80DocTypecodes_TibiaLeftAndFibulaLeftXRay,
  TibiaRightAndFibulaRightXRay: C80DocTypecodes_TibiaRightAndFibulaRightXRay,
  ToesBilateralXRay: C80DocTypecodes_ToesBilateralXRay,
  ToesLeftXRay: C80DocTypecodes_ToesLeftXRay,
  ToesRightXRay: C80DocTypecodes_ToesRightXRay,
  WristBilateralXRay: C80DocTypecodes_WristBilateralXRay,
  WristLeftXRay: C80DocTypecodes_WristLeftXRay,
  WristRightXRay: C80DocTypecodes_WristRightXRay,
  ZygomaticArchBilateralXRay: C80DocTypecodes_ZygomaticArchBilateralXRay,
  ZygomaticArchLeftXRay: C80DocTypecodes_ZygomaticArchLeftXRay,
  ZygomaticArchRightXRay: C80DocTypecodes_ZygomaticArchRightXRay,
  BreastBilateralMammogramScreening: C80DocTypecodes_BreastBilateralMammogramScreening,
  BreastLeftMammogramScreening: C80DocTypecodes_BreastLeftMammogramScreening,
  BreastRightMammogramScreening: C80DocTypecodes_BreastRightMammogramScreening,
  FemoralArteryBilateralFluoroscopicAngiogramRunoffWContrastIA: C80DocTypecodes_FemoralArteryBilateralFluoroscopicAngiogramRunoffWContrastIA,
  FemoralArteryLeftFluoroscopicAngiogramRunoffWContrastIA: C80DocTypecodes_FemoralArteryLeftFluoroscopicAngiogramRunoffWContrastIA,
  FemoralArteryRightFluoroscopicAngiogramRunoffWContrastIA: C80DocTypecodes_FemoralArteryRightFluoroscopicAngiogramRunoffWContrastIA,
  ThoracicOutletVesselsBilateralMRIAngiogramWContrastIV: C80DocTypecodes_ThoracicOutletVesselsBilateralMRIAngiogramWContrastIV,
  ThoracicOutletVesselsLeftMRIAngiogramWContrastIV: C80DocTypecodes_ThoracicOutletVesselsLeftMRIAngiogramWContrastIV,
  ThoracicOutletVesselsRightMRIAngiogramWContrastIV: C80DocTypecodes_ThoracicOutletVesselsRightMRIAngiogramWContrastIV,
  ExtremityBilateralCTWContrastIV: C80DocTypecodes_ExtremityBilateralCTWContrastIV,
  ExtremityLeftCTWContrastIV: C80DocTypecodes_ExtremityLeftCTWContrastIV,
  ExtremityRightCTWContrastIV: C80DocTypecodes_ExtremityRightCTWContrastIV,
  AnkleBilateralMRIWAndWOContrastIV: C80DocTypecodes_AnkleBilateralMRIWAndWOContrastIV,
  AnkleLeftMRIWAndWOContrastIV: C80DocTypecodes_AnkleLeftMRIWAndWOContrastIV,
  AnkleRightMRIWAndWOContrastIV: C80DocTypecodes_AnkleRightMRIWAndWOContrastIV,
  ThoracicOutletBilateralMRIWAndWOContrastIV: C80DocTypecodes_ThoracicOutletBilateralMRIWAndWOContrastIV,
  ThoracicOutletLeftMRIWAndWOContrastIV: C80DocTypecodes_ThoracicOutletLeftMRIWAndWOContrastIV,
  ThoracicOutletRightMRIWAndWOContrastIV: C80DocTypecodes_ThoracicOutletRightMRIWAndWOContrastIV,
  ElbowBilateralMRIWAndWOContrastIV: C80DocTypecodes_ElbowBilateralMRIWAndWOContrastIV,
  ElbowLeftMRIWAndWOContrastIV: C80DocTypecodes_ElbowLeftMRIWAndWOContrastIV,
  ElbowRightMRIWAndWOContrastIV: C80DocTypecodes_ElbowRightMRIWAndWOContrastIV,
  ThighBilateralMRIWAndWOContrastIV: C80DocTypecodes_ThighBilateralMRIWAndWOContrastIV,
  ThighLeftMRIWAndWOContrastIV: C80DocTypecodes_ThighLeftMRIWAndWOContrastIV,
  ThighRightMRIWAndWOContrastIV: C80DocTypecodes_ThighRightMRIWAndWOContrastIV,
  KneeBilateralMRIWAndWOContrastIV: C80DocTypecodes_KneeBilateralMRIWAndWOContrastIV,
  KneeLeftMRIWAndWOContrastIV: C80DocTypecodes_KneeLeftMRIWAndWOContrastIV,
  KneeRightMRIWAndWOContrastIV: C80DocTypecodes_KneeRightMRIWAndWOContrastIV,
  ShoulderBilateralMRIWAndWOContrastIV: C80DocTypecodes_ShoulderBilateralMRIWAndWOContrastIV,
  ShoulderLeftMRIWAndWOContrastIV: C80DocTypecodes_ShoulderLeftMRIWAndWOContrastIV,
  ShoulderRightMRIWAndWOContrastIV: C80DocTypecodes_ShoulderRightMRIWAndWOContrastIV,
  WristBilateralMRIWAndWOContrastIV: C80DocTypecodes_WristBilateralMRIWAndWOContrastIV,
  WristLeftMRIWAndWOContrastIV: C80DocTypecodes_WristLeftMRIWAndWOContrastIV,
  WristRightMRIWAndWOContrastIV: C80DocTypecodes_WristRightMRIWAndWOContrastIV,
  AnkleBilateralMRI: C80DocTypecodes_AnkleBilateralMRI,
  AnkleLeftMRI: C80DocTypecodes_AnkleLeftMRI,
  AnkleRightMRI: C80DocTypecodes_AnkleRightMRI,
  ThoracicOutletBilateralMRI: C80DocTypecodes_ThoracicOutletBilateralMRI,
  ThoracicOutletLeftMRI: C80DocTypecodes_ThoracicOutletLeftMRI,
  ThoracicOutletRightMRI: C80DocTypecodes_ThoracicOutletRightMRI,
  BreastBilateralUS: C80DocTypecodes_BreastBilateralUS,
  BreastLeftUS: C80DocTypecodes_BreastLeftUS,
  BreastRightUS: C80DocTypecodes_BreastRightUS,
  CarotidArteryBilateralUS: C80DocTypecodes_CarotidArteryBilateralUS,
  CarotidArteryLeftUS: C80DocTypecodes_CarotidArteryLeftUS,
  CarotidArteryRightUS: C80DocTypecodes_CarotidArteryRightUS,
  ElbowBilateralMRI: C80DocTypecodes_ElbowBilateralMRI,
  ElbowLeftMRI: C80DocTypecodes_ElbowLeftMRI,
  ElbowRightMRI: C80DocTypecodes_ElbowRightMRI,
  ExtremityBilateralUS: C80DocTypecodes_ExtremityBilateralUS,
  ExtremityBilateralCT: C80DocTypecodes_ExtremityBilateralCT,
  ExtremityLeftUS: C80DocTypecodes_ExtremityLeftUS,
  ExtremityLeftCT: C80DocTypecodes_ExtremityLeftCT,
  LowerExtremityJointBilateralMRI: C80DocTypecodes_LowerExtremityJointBilateralMRI,
  LowerExtremityJointLeftMRI: C80DocTypecodes_LowerExtremityJointLeftMRI,
  LowerExtremityJointRightMRI: C80DocTypecodes_LowerExtremityJointRightMRI,
  ExtremityRightUS: C80DocTypecodes_ExtremityRightUS,
  ExtremityRightCT: C80DocTypecodes_ExtremityRightCT,
  UpperExtremityBilateralMRI: C80DocTypecodes_UpperExtremityBilateralMRI,
  UpperExtremityLeftMRI: C80DocTypecodes_UpperExtremityLeftMRI,
  UpperExtremityRightMRI: C80DocTypecodes_UpperExtremityRightMRI,
  ThighBilateralMRI: C80DocTypecodes_ThighBilateralMRI,
  ThighLeftMRI: C80DocTypecodes_ThighLeftMRI,
  ThighRightMRI: C80DocTypecodes_ThighRightMRI,
  FingerBilateralMRI: C80DocTypecodes_FingerBilateralMRI,
  FingerLeftMRI: C80DocTypecodes_FingerLeftMRI,
  FingerRightMRI: C80DocTypecodes_FingerRightMRI,
  FootBilateralMRI: C80DocTypecodes_FootBilateralMRI,
  FootLeftMRI: C80DocTypecodes_FootLeftMRI,
  FootRightMRI: C80DocTypecodes_FootRightMRI,
  ForearmBilateralMRI: C80DocTypecodes_ForearmBilateralMRI,
  ForearmLeftMRI: C80DocTypecodes_ForearmLeftMRI,
  ForearmRightMRI: C80DocTypecodes_ForearmRightMRI,
  HandBilateralMRI: C80DocTypecodes_HandBilateralMRI,
  HandLeftMRI: C80DocTypecodes_HandLeftMRI,
  HandRightMRI: C80DocTypecodes_HandRightMRI,
  HipBilateralUS: C80DocTypecodes_HipBilateralUS,
  HipLeftUS: C80DocTypecodes_HipLeftUS,
  HipRightUS: C80DocTypecodes_HipRightUS,
  InternalAuditoryCanalBilateralXRayTomograph: C80DocTypecodes_InternalAuditoryCanalBilateralXRayTomograph,
  InternalAuditoryCanalLeftXRayTomograph: C80DocTypecodes_InternalAuditoryCanalLeftXRayTomograph,
  InternalAuditoryCanalRightXRayTomograph: C80DocTypecodes_InternalAuditoryCanalRightXRayTomograph,
  KneeBilateralMRI: C80DocTypecodes_KneeBilateralMRI,
  KneeLeftMRI: C80DocTypecodes_KneeLeftMRI,
  KneeRightMRI: C80DocTypecodes_KneeRightMRI,
  PelvisAndHipBilateralMRI: C80DocTypecodes_PelvisAndHipBilateralMRI,
  PelvisAndHipLeftMRI: C80DocTypecodes_PelvisAndHipLeftMRI,
  PelvisAndHipRightMRI: C80DocTypecodes_PelvisAndHipRightMRI,
  PoplitealSpaceBilateralUS: C80DocTypecodes_PoplitealSpaceBilateralUS,
  PoplitealSpaceLeftUS: C80DocTypecodes_PoplitealSpaceLeftUS,
  PoplitealSpaceRightUS: C80DocTypecodes_PoplitealSpaceRightUS,
  ShoulderBilateralUS: C80DocTypecodes_ShoulderBilateralUS,
  ShoulderBilateralMRI: C80DocTypecodes_ShoulderBilateralMRI,
  ShoulderLeftUS: C80DocTypecodes_ShoulderLeftUS,
  ShoulderLeftMRI: C80DocTypecodes_ShoulderLeftMRI,
  ShoulderRightUS: C80DocTypecodes_ShoulderRightUS,
  ShoulderRightMRI: C80DocTypecodes_ShoulderRightMRI,
  ScrotumAndTesticleBilateralUS: C80DocTypecodes_ScrotumAndTesticleBilateralUS,
  ScrotumAndTesticleLeftUS: C80DocTypecodes_ScrotumAndTesticleLeftUS,
  ScrotumAndTesticleRightUS: C80DocTypecodes_ScrotumAndTesticleRightUS,
  WristBilateralMRI: C80DocTypecodes_WristBilateralMRI,
  WristBilateralUS: C80DocTypecodes_WristBilateralUS,
  WristLeftMRI: C80DocTypecodes_WristLeftMRI,
  WristLeftUS: C80DocTypecodes_WristLeftUS,
  WristRightMRI: C80DocTypecodes_WristRightMRI,
  WristRightUS: C80DocTypecodes_WristRightUS,
  KneeBilateralXRayMerchants: C80DocTypecodes_KneeBilateralXRayMerchants,
  KneeLeftXRayMerchants: C80DocTypecodes_KneeLeftXRayMerchants,
  KneeRightXRayMerchants: C80DocTypecodes_KneeRightXRayMerchants,
  BreastBilateralUSLimited: C80DocTypecodes_BreastBilateralUSLimited,
  BreastBilateralMammogramLimited: C80DocTypecodes_BreastBilateralMammogramLimited,
  BreastLeftUSLimited: C80DocTypecodes_BreastLeftUSLimited,
  BreastLeftMammogramLimited: C80DocTypecodes_BreastLeftMammogramLimited,
  BreastRightUSLimited: C80DocTypecodes_BreastRightUSLimited,
  BreastRightMammogramLimited: C80DocTypecodes_BreastRightMammogramLimited,
  MammogramGuidanceStereotacticForBiopsyOfBreastBilateral: C80DocTypecodes_MammogramGuidanceStereotacticForBiopsyOfBreastBilateral,
  MammogramGuidanceStereotacticForBiopsyOfBreastLeft: C80DocTypecodes_MammogramGuidanceStereotacticForBiopsyOfBreastLeft,
  MammogramGuidanceStereotacticForBiopsyOfBreastRight: C80DocTypecodes_MammogramGuidanceStereotacticForBiopsyOfBreastRight,
  FluoroscopicAngiogramGuidanceForRepositionOfCatheterInCentralVeinBilateralWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForRepositionOfCatheterInCentralVeinBilateralWContrastIV,
  FluoroscopicAngiogramGuidanceForRepositionOfCatheterInCentralVeinLeftWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForRepositionOfCatheterInCentralVeinLeftWContrastIV,
  FluoroscopicAngiogramGuidanceForRepositionOfCatheterInCentralVeinRightWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForRepositionOfCatheterInCentralVeinRightWContrastIV,
  FluoroscopicAngiogramGuidanceForAtherectomyOfVeinBilateralWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForAtherectomyOfVeinBilateralWContrastIV,
  FluoroscopicAngiogramGuidanceForAtherectomyOfVeinLeftWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForAtherectomyOfVeinLeftWContrastIV,
  FluoroscopicAngiogramGuidanceForAtherectomyOfVeinRightWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForAtherectomyOfVeinRightWContrastIV,
  FluoroscopicAngiogramGuidanceForPlacementOfStentInVeinBilateral: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInVeinBilateral,
  FluoroscopicAngiogramGuidanceForPlacementOfStentInVeinLeft: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInVeinLeft,
  FluoroscopicAngiogramGuidanceForPlacementOfStentInVeinRight: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInVeinRight,
  FluoroscopicAngiogramGuidanceForPlacementOfLongtermPeripheralCatheterInCentralVeinBilateral: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfLongtermPeripheralCatheterInCentralVeinBilateral,
  FluoroscopicAngiogramGuidanceForPlacementOfLongtermPeripheralCatheterInCentralVeinLeft: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfLongtermPeripheralCatheterInCentralVeinLeft,
  FluoroscopicAngiogramGuidanceForPlacementOfLongtermPeripheralCatheterInCentralVeinRight: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfLongtermPeripheralCatheterInCentralVeinRight,
  GuidanceForPlacementOfLargeBoreCatheterIntoVesselInCentralVeinBilateral: C80DocTypecodes_GuidanceForPlacementOfLargeBoreCatheterIntoVesselInCentralVeinBilateral,
  GuidanceForPlacementOfLargeBoreCatheterIntoVesselInCentralVeinLeft: C80DocTypecodes_GuidanceForPlacementOfLargeBoreCatheterIntoVesselInCentralVeinLeft,
  GuidanceForPlacementOfLargeBoreCatheterIntoVesselInCentralVeinRight: C80DocTypecodes_GuidanceForPlacementOfLargeBoreCatheterIntoVesselInCentralVeinRight,
  FluoroscopicAngiogramGuidanceForPlacementOfCatheterInCentralVeinBilateralWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInCentralVeinBilateralWContrastIV,
  FluoroscopicAngiogramGuidanceForPlacementOfCatheterInCentralVeinLeftWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInCentralVeinLeftWContrastIV,
  FluoroscopicAngiogramGuidanceForPlacementOfCatheterInCentralVeinRightWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInCentralVeinRightWContrastIV,
  USGuidanceForNeedleLocalizationOfBreastBilateral: C80DocTypecodes_USGuidanceForNeedleLocalizationOfBreastBilateral,
  USGuidanceForNeedleLocalizationOfBreastLeft: C80DocTypecodes_USGuidanceForNeedleLocalizationOfBreastLeft,
  MammogramGuidanceForNeedleLocalizationOfMassOfBreastBilateral: C80DocTypecodes_MammogramGuidanceForNeedleLocalizationOfMassOfBreastBilateral,
  MammogramGuidanceForNeedleLocalizationOfMassOfBreastLeft: C80DocTypecodes_MammogramGuidanceForNeedleLocalizationOfMassOfBreastLeft,
  MammogramGuidanceForNeedleLocalizationOfMassOfBreastRight: C80DocTypecodes_MammogramGuidanceForNeedleLocalizationOfMassOfBreastRight,
  USGuidanceForNeedleLocalizationOfBreastRight: C80DocTypecodes_USGuidanceForNeedleLocalizationOfBreastRight,
  CTGuidanceForInjectionOfSacroiliacJointBilateral: C80DocTypecodes_CTGuidanceForInjectionOfSacroiliacJointBilateral,
  CTGuidanceForInjectionOfSacroiliacJointLeft: C80DocTypecodes_CTGuidanceForInjectionOfSacroiliacJointLeft,
  CTGuidanceForInjectionOfSacroiliacJointRight: C80DocTypecodes_CTGuidanceForInjectionOfSacroiliacJointRight,
  FluoroscopyGuidanceForInjectionOfSpineFacetJointBilateral: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineFacetJointBilateral,
  FluoroscopyGuidanceForInjectionOfSpineFacetJointLeft: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineFacetJointLeft,
  FluoroscopyGuidanceForInjectionOfSpineFacetJointRight: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineFacetJointRight,
  USGuidanceForDrainageOfExtremityBilateral: C80DocTypecodes_USGuidanceForDrainageOfExtremityBilateral,
  USGuidanceForDrainageOfExtremityLeft: C80DocTypecodes_USGuidanceForDrainageOfExtremityLeft,
  USGuidanceForDrainageOfExtremityRight: C80DocTypecodes_USGuidanceForDrainageOfExtremityRight,
  USGuidanceForDrainageOfKidneyBilateral: C80DocTypecodes_USGuidanceForDrainageOfKidneyBilateral,
  USGuidanceForDrainageOfKidneyLeft: C80DocTypecodes_USGuidanceForDrainageOfKidneyLeft,
  USGuidanceForDrainageOfKidneyRight: C80DocTypecodes_USGuidanceForDrainageOfKidneyRight,
  FluoroscopicAngiogramGuidanceForChangeOfCentralCatheterInCentralVeinBilateralWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForChangeOfCentralCatheterInCentralVeinBilateralWContrastIV,
  FluoroscopicAngiogramGuidanceForChangeOfCentralCatheterInCentralVeinLeftWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForChangeOfCentralCatheterInCentralVeinLeftWContrastIV,
  FluoroscopicAngiogramGuidanceForChangeOfCentralCatheterInCentralVeinRightWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForChangeOfCentralCatheterInCentralVeinRightWContrastIV,
  MammogramGuidanceForCoreNeedlePercutaneousBiopsyOfBreastBilateral: C80DocTypecodes_MammogramGuidanceForCoreNeedlePercutaneousBiopsyOfBreastBilateral,
  MammogramGuidanceForCoreNeedlePercutaneousBiopsyOfBreastLeft: C80DocTypecodes_MammogramGuidanceForCoreNeedlePercutaneousBiopsyOfBreastLeft,
  MammogramGuidanceForCoreNeedlePercutaneousBiopsyOfBreastRight: C80DocTypecodes_MammogramGuidanceForCoreNeedlePercutaneousBiopsyOfBreastRight,
  MammogramGuidanceForBiopsyOfBreastBilateral: C80DocTypecodes_MammogramGuidanceForBiopsyOfBreastBilateral,
  MammogramGuidanceForBiopsyOfBreastLeft: C80DocTypecodes_MammogramGuidanceForBiopsyOfBreastLeft,
  MammogramGuidanceForBiopsyOfBreastRight: C80DocTypecodes_MammogramGuidanceForBiopsyOfBreastRight,
  USGuidanceForBiopsyOfKidneyBilateral: C80DocTypecodes_USGuidanceForBiopsyOfKidneyBilateral,
  USGuidanceForBiopsyOfKidneyLeft: C80DocTypecodes_USGuidanceForBiopsyOfKidneyLeft,
  USGuidanceForBiopsyOfKidneyRight: C80DocTypecodes_USGuidanceForBiopsyOfKidneyRight,
  MammogramGuidanceForAspirationOfCystOfBreastBilateral: C80DocTypecodes_MammogramGuidanceForAspirationOfCystOfBreastBilateral,
  MammogramGuidanceForAspirationOfCystOfBreastLeft: C80DocTypecodes_MammogramGuidanceForAspirationOfCystOfBreastLeft,
  MammogramGuidanceForAspirationOfCystOfBreastRight: C80DocTypecodes_MammogramGuidanceForAspirationOfCystOfBreastRight,
  BreastBilateralMammogramDiagnostic: C80DocTypecodes_BreastBilateralMammogramDiagnostic,
  BreastLeftMammogramDiagnostic: C80DocTypecodes_BreastLeftMammogramDiagnostic,
  BreastRightMammogramDiagnostic: C80DocTypecodes_BreastRightMammogramDiagnostic,
  BreastBilateralMammogramDiagnosticLimited: C80DocTypecodes_BreastBilateralMammogramDiagnosticLimited,
  BreastLeftMammogramDiagnosticLimited: C80DocTypecodes_BreastLeftMammogramDiagnosticLimited,
  BreastRightMammogramDiagnosticLimited: C80DocTypecodes_BreastRightMammogramDiagnosticLimited,
  WristBilateralAndHandBilateralXRayBoneAge: C80DocTypecodes_WristBilateralAndHandBilateralXRayBoneAge,
  WristLeftAndHandLeftXRayBoneAge: C80DocTypecodes_WristLeftAndHandLeftXRayBoneAge,
  WristRightAndHandRightXRayBoneAge: C80DocTypecodes_WristRightAndHandRightXRayBoneAge,
  HandBilateralXRayArthritis: C80DocTypecodes_HandBilateralXRayArthritis,
  HandLeftXRayArthritis: C80DocTypecodes_HandLeftXRayArthritis,
  HandRightXRayArthritis: C80DocTypecodes_HandRightXRayArthritis,
  KneeBilateralXRayAPSingleViewStanding: C80DocTypecodes_KneeBilateralXRayAPSingleViewStanding,
  KneeLeftXRayAPSingleViewStanding: C80DocTypecodes_KneeLeftXRayAPSingleViewStanding,
  KneeRightXRayAPSingleViewStanding: C80DocTypecodes_KneeRightXRayAPSingleViewStanding,
  KneeBilateralXRayAPAndPAStanding: C80DocTypecodes_KneeBilateralXRayAPAndPAStanding,
  KneeLeftXRayAPAndPAStanding: C80DocTypecodes_KneeLeftXRayAPAndPAStanding,
  KneeRightXRayAPAndPAStanding: C80DocTypecodes_KneeRightXRayAPAndPAStanding,
  KneeBilateralXRayAPAndLateralStanding: C80DocTypecodes_KneeBilateralXRayAPAndLateralStanding,
  KneeLeftXRayAPAndLateralStanding: C80DocTypecodes_KneeLeftXRayAPAndLateralStanding,
  KneeRightXRayAPAndLateralStanding: C80DocTypecodes_KneeRightXRayAPAndLateralStanding,
  BrachiocephalicArteryLeftFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_BrachiocephalicArteryLeftFluoroscopicAngiogramAngioplastyWContrastIA,
  BrachiocephalicArteryRightFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_BrachiocephalicArteryRightFluoroscopicAngiogramAngioplastyWContrastIA,
  IliacArteryBilateralFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_IliacArteryBilateralFluoroscopicAngiogramAngioplastyWContrastIA,
  IliacArteryLeftFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_IliacArteryLeftFluoroscopicAngiogramAngioplastyWContrastIA,
  IliacArteryRightFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_IliacArteryRightFluoroscopicAngiogramAngioplastyWContrastIA,
  TibialArteryBilateralFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_TibialArteryBilateralFluoroscopicAngiogramAngioplastyWContrastIA,
  TibialArteryLeftFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_TibialArteryLeftFluoroscopicAngiogramAngioplastyWContrastIA,
  TibialArteryRightFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_TibialArteryRightFluoroscopicAngiogramAngioplastyWContrastIA,
  AdministrationOfVasodilatorIntoCatheterOfVeinBilateral: C80DocTypecodes_AdministrationOfVasodilatorIntoCatheterOfVeinBilateral,
  AdministrationOfVasodilatorIntoCatheterOfVeinLeft: C80DocTypecodes_AdministrationOfVasodilatorIntoCatheterOfVeinLeft,
  AdministrationOfVasodilatorIntoCatheterOfVeinRight: C80DocTypecodes_AdministrationOfVasodilatorIntoCatheterOfVeinRight,
  HandBilateralXRay3Views: C80DocTypecodes_HandBilateralXRay3Views,
  HandLeftXRay3Views: C80DocTypecodes_HandLeftXRay3Views,
  HandRightXRay3Views: C80DocTypecodes_HandRightXRay3Views,
  ShoulderBilateralXRay3Views: C80DocTypecodes_ShoulderBilateralXRay3Views,
  ShoulderLeftXRay3Views: C80DocTypecodes_ShoulderLeftXRay3Views,
  ShoulderRightXRay3Views: C80DocTypecodes_ShoulderRightXRay3Views,
  AnkleBilateralXRay2Views: C80DocTypecodes_AnkleBilateralXRay2Views,
  AnkleLeftXRay2Views: C80DocTypecodes_AnkleLeftXRay2Views,
  AnkleRightXRay2Views: C80DocTypecodes_AnkleRightXRay2Views,
  HandBilateralXRay2Views: C80DocTypecodes_HandBilateralXRay2Views,
  HandLeftXRay2Views: C80DocTypecodes_HandLeftXRay2Views,
  HandRightXRay2Views: C80DocTypecodes_HandRightXRay2Views,
  HumerusBilateralXRay2Views: C80DocTypecodes_HumerusBilateralXRay2Views,
  HumerusLeftXRay2Views: C80DocTypecodes_HumerusLeftXRay2Views,
  HumerusRightXRay2Views: C80DocTypecodes_HumerusRightXRay2Views,
  KneeBilateralXRay2Views: C80DocTypecodes_KneeBilateralXRay2Views,
  KneeLeftXRay2Views: C80DocTypecodes_KneeLeftXRay2Views,
  KneeRightXRay2Views: C80DocTypecodes_KneeRightXRay2Views,
  PatellaBilateralXRay2Views: C80DocTypecodes_PatellaBilateralXRay2Views,
  PatellaLeftXRay2Views: C80DocTypecodes_PatellaLeftXRay2Views,
  PatellaRightXRay2Views: C80DocTypecodes_PatellaRightXRay2Views,
  HipBilateralXRaySingleView: C80DocTypecodes_HipBilateralXRaySingleView,
  HipLeftXRaySingleView: C80DocTypecodes_HipLeftXRaySingleView,
  HipRightXRaySingleView: C80DocTypecodes_HipRightXRaySingleView,
  VAL18HydroxydeoxycorticosteroneMassTimeIn24HourUrine: C80DocTypecodes_VAL18HydroxydeoxycorticosteroneMassTimeIn24HourUrine,
  PelvisXRay: C80DocTypecodes_PelvisXRay,
  SkullXRay: C80DocTypecodes_SkullXRay,
  KneeXRay: C80DocTypecodes_KneeXRay,
  SpineCT: C80DocTypecodes_SpineCT,
  HumerusXRay: C80DocTypecodes_HumerusXRay,
  PhysicianEmergencyDepartmentNote: C80DocTypecodes_PhysicianEmergencyDepartmentNote,
  PhysicianConsultingProgressNote: C80DocTypecodes_PhysicianConsultingProgressNote,
  ProviderUnspecifiedProcedureNote: C80DocTypecodes_ProviderUnspecifiedProcedureNote,
  SpeechLanguagePathologyNote: C80DocTypecodes_SpeechLanguagePathologyNote,
  DentistInitialAssessmentNote: C80DocTypecodes_DentistInitialAssessmentNote,
  PhysicianOperationNote: C80DocTypecodes_PhysicianOperationNote,
  DischargeNote: C80DocTypecodes_DischargeNote,
  NursePractitionerProgressNote: C80DocTypecodes_NursePractitionerProgressNote,
  JointMRI: C80DocTypecodes_JointMRI,
  DentistProcedureNote: C80DocTypecodes_DentistProcedureNote,
  OccupationalTherapyNote: C80DocTypecodes_OccupationalTherapyNote,
  PhysicalTherapyNote: C80DocTypecodes_PhysicalTherapyNote,
  ChiropracticMedicineProgressNote: C80DocTypecodes_ChiropracticMedicineProgressNote,
  ChiropracticMedicineInitialAssessmentNote: C80DocTypecodes_ChiropracticMedicineInitialAssessmentNote,
  HandXRay: C80DocTypecodes_HandXRay,
  DentistOperationNote: C80DocTypecodes_DentistOperationNote,
  SpineXRay: C80DocTypecodes_SpineXRay,
  LiverUS: C80DocTypecodes_LiverUS,
  AudiologyStudy: C80DocTypecodes_AudiologyStudy,
  PhysicianTransferNote: C80DocTypecodes_PhysicianTransferNote,
  DentistryProgressNote: C80DocTypecodes_DentistryProgressNote,
  DentistryNote: C80DocTypecodes_DentistryNote,
  NursePractitionerInitialAssessmentNote: C80DocTypecodes_NursePractitionerInitialAssessmentNote,
  NurseDischargeAssessment: C80DocTypecodes_NurseDischargeAssessment,
  NurseProgressNote: C80DocTypecodes_NurseProgressNote,
  PodiatryOperationNote: C80DocTypecodes_PodiatryOperationNote,
  PodiatryProcedureNote: C80DocTypecodes_PodiatryProcedureNote,
  PhysicianHistoryAndPhysicalNote: C80DocTypecodes_PhysicianHistoryAndPhysicalNote,
  PsychiatryProgressNote: C80DocTypecodes_PsychiatryProgressNote,
  PsychiatryNote: C80DocTypecodes_PsychiatryNote,
  PerimetryStudy: C80DocTypecodes_PerimetryStudy,
  TonometryStudy: C80DocTypecodes_TonometryStudy,
  VisualAcuityStudy: C80DocTypecodes_VisualAcuityStudy,
  HeterophoriaStudy: C80DocTypecodes_HeterophoriaStudy,
  PolysomnographySleepStudy: C80DocTypecodes_PolysomnographySleepStudy,
  PsychiatryInitialAssessmentNote: C80DocTypecodes_PsychiatryInitialAssessmentNote,
  ProviderUnspecifiedInitialAssessment: C80DocTypecodes_ProviderUnspecifiedInitialAssessment,
  NurseTransferNote: C80DocTypecodes_NurseTransferNote,
  SocialWorkNote: C80DocTypecodes_SocialWorkNote,
  PhysicianAttendingInitialAssessmentNote: C80DocTypecodes_PhysicianAttendingInitialAssessmentNote,
  PhysicianAttendingDischargeSummary: C80DocTypecodes_PhysicianAttendingDischargeSummary,
  SocialWorkProgressNote: C80DocTypecodes_SocialWorkProgressNote,
  PhotoDocumentationEyeRight: C80DocTypecodes_PhotoDocumentationEyeRight,
  PhotoDocumentationEyeLeft: C80DocTypecodes_PhotoDocumentationEyeLeft,
  SpeechTherapyServiceAttachment: C80DocTypecodes_SpeechTherapyServiceAttachment,
  ChestCTWOContrast: C80DocTypecodes_ChestCTWOContrast,
  EyeUltrasoundStudy: C80DocTypecodes_EyeUltrasoundStudy,
  DialysisRecords: C80DocTypecodes_DialysisRecords,
  NeonatalIntensiveCareRecords: C80DocTypecodes_NeonatalIntensiveCareRecords,
  CriticalCareRecords: C80DocTypecodes_CriticalCareRecords,
  PerioperativeRecords: C80DocTypecodes_PerioperativeRecords,
  NurseInitialAssessmentNote: C80DocTypecodes_NurseInitialAssessmentNote,
  NystagmogramStudy: C80DocTypecodes_NystagmogramStudy,
  NerveConductionStudy: C80DocTypecodes_NerveConductionStudy,
  PeritoneoscopyStudy: C80DocTypecodes_PeritoneoscopyStudy,
  ColposcopyStudy: C80DocTypecodes_ColposcopyStudy,
  DentistDischargeSummary: C80DocTypecodes_DentistDischargeSummary,
  CTGuidanceForAbscessDrainageOfUnspecifiedBodyRegion: C80DocTypecodes_CTGuidanceForAbscessDrainageOfUnspecifiedBodyRegion,
  CTGuidanceForInjectionOfSpineFacetJoint: C80DocTypecodes_CTGuidanceForInjectionOfSpineFacetJoint,
  CTGuidanceForFineNeedleAspirationOfUnspecifiedBodyRegion: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfUnspecifiedBodyRegion,
  CTGuidanceForRadiationTreatmentOfUnspecifiedBodyRegionWContrastIV: C80DocTypecodes_CTGuidanceForRadiationTreatmentOfUnspecifiedBodyRegionWContrastIV,
  CTGuidanceForRadiationTreatmentOfUnspecifiedBodyRegionWOContrast: C80DocTypecodes_CTGuidanceForRadiationTreatmentOfUnspecifiedBodyRegionWOContrast,
  InternalAuditoryCanalCTWContrastIV: C80DocTypecodes_InternalAuditoryCanalCTWContrastIV,
  InternalAuditoryCanalCTWOContrast: C80DocTypecodes_InternalAuditoryCanalCTWOContrast,
  NasopharynxAndNeckCTWOContrast: C80DocTypecodes_NasopharynxAndNeckCTWOContrast,
  NeckCTWAndWOContrastIV: C80DocTypecodes_NeckCTWAndWOContrastIV,
  OrbitBilateralCTWOContrast: C80DocTypecodes_OrbitBilateralCTWOContrast,
  SinusesCT: C80DocTypecodes_SinusesCT,
  PetrousBoneCTWOContrast: C80DocTypecodes_PetrousBoneCTWOContrast,
  PituitaryAndSellaTurcicaCTWContrastIV: C80DocTypecodes_PituitaryAndSellaTurcicaCTWContrastIV,
  PituitaryAndSellaTurcicaCTWOContrast: C80DocTypecodes_PituitaryAndSellaTurcicaCTWOContrast,
  SpineCervicalCTWOContrast: C80DocTypecodes_SpineCervicalCTWOContrast,
  HeadVesselsCTAngiogramWAndWOContrastIV: C80DocTypecodes_HeadVesselsCTAngiogramWAndWOContrastIV,
  NeckVesselsCTAngiogramWAndWOContrastIV: C80DocTypecodes_NeckVesselsCTAngiogramWAndWOContrastIV,
  CTGuidanceForFineNeedleAspirationOfLung: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfLung,
  SpineThoracicCTWContrastIT: C80DocTypecodes_SpineThoracicCTWContrastIT,
  SpineThoracicCTWOContrast: C80DocTypecodes_SpineThoracicCTWOContrast,
  ChestCTWAndWOContrastIV: C80DocTypecodes_ChestCTWAndWOContrastIV,
  AbdomenCTWContrast: C80DocTypecodes_AbdomenCTWContrast,
  SmallBowelCTViewsEnteroclysisWContrastPOViaDuodenalIntubation: C80DocTypecodes_SmallBowelCTViewsEnteroclysisWContrastPOViaDuodenalIntubation,
  CTGuidanceForBiopsyOfAbdomen: C80DocTypecodes_CTGuidanceForBiopsyOfAbdomen,
  CTGuidanceForFineNeedleAspirationOfAbdomen: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfAbdomen,
  CTGuidanceForFineNeedleAspirationOfLiver: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfLiver,
  CTGuidanceForBiopsyOfPancreas: C80DocTypecodes_CTGuidanceForBiopsyOfPancreas,
  CTGuidanceForFineNeedleAspirationOfPancreas: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfPancreas,
  CTGuidanceForFineNeedleAspirationOfPelvis: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfPelvis,
  CTGuidanceForBiopsyOfKidneyBilateral: C80DocTypecodes_CTGuidanceForBiopsyOfKidneyBilateral,
  CTGuidanceForFineNeedleAspirationOfKidneyBilateral: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfKidneyBilateral,
  CTGuidanceForBiopsyOfSpleen: C80DocTypecodes_CTGuidanceForBiopsyOfSpleen,
  CTGuidanceForFineNeedleAspirationOfSpleen: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfSpleen,
  LiverCTWOContrast: C80DocTypecodes_LiverCTWOContrast,
  LiverCTWAndWOContrastIV: C80DocTypecodes_LiverCTWAndWOContrastIV,
  PancreasCTWOContrast: C80DocTypecodes_PancreasCTWOContrast,
  PancreasCTWAndWOContrastIV: C80DocTypecodes_PancreasCTWAndWOContrastIV,
  PelvisCTWOContrast: C80DocTypecodes_PelvisCTWOContrast,
  PelvisCTWAndWOContrastIV: C80DocTypecodes_PelvisCTWAndWOContrastIV,
  SacroiliacJointCT: C80DocTypecodes_SacroiliacJointCT,
  SpineLumbarCTWOContrast: C80DocTypecodes_SpineLumbarCTWOContrast,
  SpleenCTWOContrast: C80DocTypecodes_SpleenCTWOContrast,
  SpleenCTWContrastIV: C80DocTypecodes_SpleenCTWContrastIV,
  PelvisVesselsCTAngiogramWAndWOContrastIV: C80DocTypecodes_PelvisVesselsCTAngiogramWAndWOContrastIV,
  LowerExtremityCTWContrastIV: C80DocTypecodes_LowerExtremityCTWContrastIV,
  LowerExtremityCTWOContrast: C80DocTypecodes_LowerExtremityCTWOContrast,
  UpperExtremityCTWContrastIV: C80DocTypecodes_UpperExtremityCTWContrastIV,
  UpperExtremityCTWOContrast: C80DocTypecodes_UpperExtremityCTWOContrast,
  FluoroscopyGuidanceForRemovalOfForeignBodyFromUnspecifiedBodyRegion: C80DocTypecodes_FluoroscopyGuidanceForRemovalOfForeignBodyFromUnspecifiedBodyRegion,
  FluoroscopyGuidanceForProcedureOfUnspecifiedBodyRegion: C80DocTypecodes_FluoroscopyGuidanceForProcedureOfUnspecifiedBodyRegion,
  HeadCisternFluoroscopyVideoWContrast: C80DocTypecodes_HeadCisternFluoroscopyVideoWContrast,
  ChestFluoroscopy: C80DocTypecodes_ChestFluoroscopy,
  DiaphragmFluoroscopyMotion: C80DocTypecodes_DiaphragmFluoroscopyMotion,
  EsophagusFluoroscopyWBariumContrastPO: C80DocTypecodes_EsophagusFluoroscopyWBariumContrastPO,
  FluoroscopyGuidanceForBiopsyOfLung: C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfLung,
  GastrointestineUpperFluoroscopyAndAPWContrastPO: C80DocTypecodes_GastrointestineUpperFluoroscopyAndAPWContrastPO,
  ColonFluoroscopyReductionWViewsWContrastPR: C80DocTypecodes_ColonFluoroscopyReductionWViewsWContrastPR,
  FluoroscopyGuidanceForPlacementOfTubeInGastrointestine: C80DocTypecodes_FluoroscopyGuidanceForPlacementOfTubeInGastrointestine,
  FluoroscopyGuidanceForInjectionOfHip: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfHip,
  VesselFluoroscopicAngiogramWContrast: C80DocTypecodes_VesselFluoroscopicAngiogramWContrast,
  VeinFluoroscopicAngiogramAngioplastyWContrastIV: C80DocTypecodes_VeinFluoroscopicAngiogramAngioplastyWContrastIV,
  VeinFluoroscopicAngiogramAdditionalAngioplastyWContrastIV: C80DocTypecodes_VeinFluoroscopicAngiogramAdditionalAngioplastyWContrastIV,
  UnspecifiedBodyRegionFluoroscopySingleView: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopySingleView,
  USGuidanceForPlacementOfCatheterInCentralVein: C80DocTypecodes_USGuidanceForPlacementOfCatheterInCentralVein,
  USGuidanceForPlacementOfCatheterInCentralVeinTunneled: C80DocTypecodes_USGuidanceForPlacementOfCatheterInCentralVeinTunneled,
  SuperiorVenaCavaFluoroscopicAngiogramWContrastIV: C80DocTypecodes_SuperiorVenaCavaFluoroscopicAngiogramWContrastIV,
  FluoroscopyGuidanceForChangeOfTubeInSinusTractWContrast: C80DocTypecodes_FluoroscopyGuidanceForChangeOfTubeInSinusTractWContrast,
  BiliaryDuctsAndGallbladderFluoroscopyWContrastViaTTube: C80DocTypecodes_BiliaryDuctsAndGallbladderFluoroscopyWContrastViaTTube,
  PeripheralArteryFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_PeripheralArteryFluoroscopicAngiogramAngioplastyWContrastIA,
  PeripheralArteryFluoroscopicAngiogramAdditionalAngioplastyWContrastIA: C80DocTypecodes_PeripheralArteryFluoroscopicAngiogramAdditionalAngioplastyWContrastIA,
  UnspecifiedBodyRegionFluoroscopyForShunt: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyForShunt,
  USGuidanceForCoreNeedleBiopsyOfBreast: C80DocTypecodes_USGuidanceForCoreNeedleBiopsyOfBreast,
  USGuidanceForFineNeedleBiopsyOfBreast: C80DocTypecodes_USGuidanceForFineNeedleBiopsyOfBreast,
  USGuidanceForAspirationOfCystOfBreast: C80DocTypecodes_USGuidanceForAspirationOfCystOfBreast,
  ThoracicOutletMRIWOContrast: C80DocTypecodes_ThoracicOutletMRIWOContrast,
  HeadCisternMRI: C80DocTypecodes_HeadCisternMRI,
  MRIGuidanceStereotacticForLocalizationInBrainWContrastIV: C80DocTypecodes_MRIGuidanceStereotacticForLocalizationInBrainWContrastIV,
  BrainMRIWOContrast: C80DocTypecodes_BrainMRIWOContrast,
  InternalAuditoryCanalMRIWOContrast: C80DocTypecodes_InternalAuditoryCanalMRIWOContrast,
  InternalAuditoryCanalMRIWAndWOContrastIV: C80DocTypecodes_InternalAuditoryCanalMRIWAndWOContrastIV,
  NeckMRIWOContrast: C80DocTypecodes_NeckMRIWOContrast,
  OrbitBilateralMRIWOContrast: C80DocTypecodes_OrbitBilateralMRIWOContrast,
  SinusesMRIWOContrast: C80DocTypecodes_SinusesMRIWOContrast,
  SinusesMRIWAndWOContrastIV: C80DocTypecodes_SinusesMRIWAndWOContrastIV,
  MRIGuidanceForRadiationTreatmentOfUnspecifiedBodyRegionWContrastIV: C80DocTypecodes_MRIGuidanceForRadiationTreatmentOfUnspecifiedBodyRegionWContrastIV,
  MRIGuidanceForRadiationTreatmentOfUnspecifiedBodyRegionWOContrast: C80DocTypecodes_MRIGuidanceForRadiationTreatmentOfUnspecifiedBodyRegionWOContrast,
  PituitaryAndSellaTurcicaMRIWOContrast: C80DocTypecodes_PituitaryAndSellaTurcicaMRIWOContrast,
  SpineCervicalMRIWOContrast: C80DocTypecodes_SpineCervicalMRIWOContrast,
  AbdomenMRIWOContrast: C80DocTypecodes_AbdomenMRIWOContrast,
  LiverMRIWOContrast: C80DocTypecodes_LiverMRIWOContrast,
  LiverMRIWAndWOContrastIV: C80DocTypecodes_LiverMRIWAndWOContrastIV,
  PelvisAndHipMRIWOContrast: C80DocTypecodes_PelvisAndHipMRIWOContrast,
  PelvisAndHipMRIWAndWOContrastIV: C80DocTypecodes_PelvisAndHipMRIWAndWOContrastIV,
  PelvisMRIWOContrast: C80DocTypecodes_PelvisMRIWOContrast,
  PelvisMRIWAndWOContrastIV: C80DocTypecodes_PelvisMRIWAndWOContrastIV,
  ProstateMRI: C80DocTypecodes_ProstateMRI,
  SpineLumbarMRIWContrastIV: C80DocTypecodes_SpineLumbarMRIWContrastIV,
  SpineLumbarMRIWOContrast: C80DocTypecodes_SpineLumbarMRIWOContrast,
  AnkleMRIWOContrast: C80DocTypecodes_AnkleMRIWOContrast,
  FootMRIWOContrast: C80DocTypecodes_FootMRIWOContrast,
  FootMRIWAndWOContrastIV: C80DocTypecodes_FootMRIWAndWOContrastIV,
  ForearmMRIWOContrast: C80DocTypecodes_ForearmMRIWOContrast,
  ForearmMRIWAndWOContrastIV: C80DocTypecodes_ForearmMRIWAndWOContrastIV,
  HandMRIWOContrast: C80DocTypecodes_HandMRIWOContrast,
  HandMRIWAndWOContrastIV: C80DocTypecodes_HandMRIWAndWOContrastIV,
  HipMRIWOContrast: C80DocTypecodes_HipMRIWOContrast,
  HipMRIWAndWOContrastIV: C80DocTypecodes_HipMRIWAndWOContrastIV,
  UpperArmMRIWOContrast: C80DocTypecodes_UpperArmMRIWOContrast,
  UpperArmMRIWAndWOContrastIV: C80DocTypecodes_UpperArmMRIWAndWOContrastIV,
  KneeMRIWOContrast: C80DocTypecodes_KneeMRIWOContrast,
  LowerExtremityMRI: C80DocTypecodes_LowerExtremityMRI,
  ShoulderMRIWOContrast: C80DocTypecodes_ShoulderMRIWOContrast,
  ThyroidScanAndUptakeSingle: C80DocTypecodes_ThyroidScanAndUptakeSingle,
  ThyroidScan: C80DocTypecodes_ThyroidScan,
  ScrotumAndTesticleScan: C80DocTypecodes_ScrotumAndTesticleScan,
  PulmonarySystemScanVentilationAndPerfusionWRadionuclideInhaledAndWRadionuclideIV: C80DocTypecodes_PulmonarySystemScanVentilationAndPerfusionWRadionuclideInhaledAndWRadionuclideIV,
  USGuidanceForAspirationOfCystOfUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForAspirationOfCystOfUnspecifiedBodyRegion,
  USGuidanceForDrainageOfUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForDrainageOfUnspecifiedBodyRegion,
  USGuidanceForNeedleBiopsyOfUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForNeedleBiopsyOfUnspecifiedBodyRegion,
  UnspecifiedBodyRegionUSDuringSurgery: C80DocTypecodes_UnspecifiedBodyRegionUSDuringSurgery,
  USGuidanceForInjectionOfThyroid: C80DocTypecodes_USGuidanceForInjectionOfThyroid,
  USGuidanceForAspirationOfPericardialSpace: C80DocTypecodes_USGuidanceForAspirationOfPericardialSpace,
  AbdomenUSLimited: C80DocTypecodes_AbdomenUSLimited,
  UterusAndFallopianTubesUS: C80DocTypecodes_UterusAndFallopianTubesUS,
  LiverUSDuringSurgery: C80DocTypecodes_LiverUSDuringSurgery,
  LowerExtremityUS: C80DocTypecodes_LowerExtremityUS,
  UpperExtremityUS: C80DocTypecodes_UpperExtremityUS,
  HipUSDevelopmentalJointAssessment: C80DocTypecodes_HipUSDevelopmentalJointAssessment,
  HipUSWODevelopmentalJointAssessment: C80DocTypecodes_HipUSWODevelopmentalJointAssessment,
  SpineXRayWRightBendingAndWLeftBending: C80DocTypecodes_SpineXRayWRightBendingAndWLeftBending,
  SpineThoracicAndLumbarXRayScoliosisAP: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAP,
  SpineThoracicAndLumbarXRayScoliosisAPAndLateral: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPAndLateral,
  SpineThoracicAndLumbarXRayScoliosisLateral: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisLateral,
  SpineThoracicAndLumbarXRayScoliosisStanding: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisStanding,
  TemporomandibularJointXRayTomograph: C80DocTypecodes_TemporomandibularJointXRayTomograph,
  OrbitBilateralXRayForForeignBody: C80DocTypecodes_OrbitBilateralXRayForForeignBody,
  SinusesXRayPAAndLateral: C80DocTypecodes_SinusesXRayPAAndLateral,
  SkullXRaySingleViewPortable: C80DocTypecodes_SkullXRaySingleViewPortable,
  SkullXRayPortable: C80DocTypecodes_SkullXRayPortable,
  SpineCervicalXRaySingleViewPortable: C80DocTypecodes_SpineCervicalXRaySingleViewPortable,
  SpineCervicalXRayAPSingleView: C80DocTypecodes_SpineCervicalXRayAPSingleView,
  SpineCervicalXRayAPAndLateralPortable: C80DocTypecodes_SpineCervicalXRayAPAndLateralPortable,
  SpineCervicalXRayAPPortableSingleView: C80DocTypecodes_SpineCervicalXRayAPPortableSingleView,
  SpineCervicalOdontoidAndCervicalAxisXRayAPPortableSingleView: C80DocTypecodes_SpineCervicalOdontoidAndCervicalAxisXRayAPPortableSingleView,
  ZygomaticArchBilateralXRayPortable: C80DocTypecodes_ZygomaticArchBilateralXRayPortable,
  ZygomaticArchXRayPortable: C80DocTypecodes_ZygomaticArchXRayPortable,
  ChestXRayRightAndLeftObliquePortable: C80DocTypecodes_ChestXRayRightAndLeftObliquePortable,
  ChestXRayAPLateralDecubitus: C80DocTypecodes_ChestXRayAPLateralDecubitus,
  ChestXRayAPLateralDecubitusPortable: C80DocTypecodes_ChestXRayAPLateralDecubitusPortable,
  ChestXRayWInspirationAndExpiration: C80DocTypecodes_ChestXRayWInspirationAndExpiration,
  ChestXRayLeftLateral: C80DocTypecodes_ChestXRayLeftLateral,
  ChestXRayLeftLateralPortable: C80DocTypecodes_ChestXRayLeftLateralPortable,
  ChestXRayRightOrLeftObliquePortable: C80DocTypecodes_ChestXRayRightOrLeftObliquePortable,
  ChestXRayRightOrLeftOblique: C80DocTypecodes_ChestXRayRightOrLeftOblique,
  ChestXRayPAAndLateralAndLordoticUpright: C80DocTypecodes_ChestXRayPAAndLateralAndLordoticUpright,
  ChestXRayPAAndLateralAndRightObliqueAndLeftOblique: C80DocTypecodes_ChestXRayPAAndLateralAndRightObliqueAndLeftOblique,
  ChestXRayPAAndLateralAndRightObliqueAndLeftObliquePortable: C80DocTypecodes_ChestXRayPAAndLateralAndRightObliqueAndLeftObliquePortable,
  ChestXRayPAAndLateralAndRightOrLeftOblique: C80DocTypecodes_ChestXRayPAAndLateralAndRightOrLeftOblique,
  ChestXRay: C80DocTypecodes_ChestXRay,
  ChestXRayPortable: C80DocTypecodes_ChestXRayPortable,
  RibsXRayPortable: C80DocTypecodes_RibsXRayPortable,
  ShoulderXRaySingleView: C80DocTypecodes_ShoulderXRaySingleView,
  ShoulderXRaySingleViewPortable: C80DocTypecodes_ShoulderXRaySingleViewPortable,
  ShoulderXRay5Views: C80DocTypecodes_ShoulderXRay5Views,
  ShoulderXRayWestPoint: C80DocTypecodes_ShoulderXRayWestPoint,
  SpineThoracicXRayAPSingleView: C80DocTypecodes_SpineThoracicXRayAPSingleView,
  SpineThoracicXRayAPAndLateral: C80DocTypecodes_SpineThoracicXRayAPAndLateral,
  SpineThoracicXRayAPAndLateralPortable: C80DocTypecodes_SpineThoracicXRayAPAndLateralPortable,
  SpineThoracicXRayAPPortableSingleView: C80DocTypecodes_SpineThoracicXRayAPPortableSingleView,
  SpineThoracicXRayLateral: C80DocTypecodes_SpineThoracicXRayLateral,
  SpineThoracicXRayLateralPortable: C80DocTypecodes_SpineThoracicXRayLateralPortable,
  SpineThoracicXRayObliqueSingleView: C80DocTypecodes_SpineThoracicXRayObliqueSingleView,
  SpineThoracicXRayObliquePortable: C80DocTypecodes_SpineThoracicXRayObliquePortable,
  KidneyBilateralXRayTomograph3ViewsWContrastIV: C80DocTypecodes_KidneyBilateralXRayTomograph3ViewsWContrastIV,
  KidneyBilateralFluoroscopyWContrastRetrogradeViaUrethra: C80DocTypecodes_KidneyBilateralFluoroscopyWContrastRetrogradeViaUrethra,
  AbdomenXRayTomograph: C80DocTypecodes_AbdomenXRayTomograph,
  AbdomenXRayAPAndLateralCrosstablePortable: C80DocTypecodes_AbdomenXRayAPAndLateralCrosstablePortable,
  AcetabulumBilateralXRayPortable: C80DocTypecodes_AcetabulumBilateralXRayPortable,
  AcetabulumXRayPortable: C80DocTypecodes_AcetabulumXRayPortable,
  PelvisXRay3Views: C80DocTypecodes_PelvisXRay3Views,
  PelvisAndHipXRay: C80DocTypecodes_PelvisAndHipXRay,
  PelvisAndHipBilateralXRay: C80DocTypecodes_PelvisAndHipBilateralXRay,
  PelvisAndHipBilateralXRayMaxAbduction: C80DocTypecodes_PelvisAndHipBilateralXRayMaxAbduction,
  PelvisAndHipXRayAPAndLateralFrog: C80DocTypecodes_PelvisAndHipXRayAPAndLateralFrog,
  PelvisXRayInletAndOutlet: C80DocTypecodes_PelvisXRayInletAndOutlet,
  PelvisXRayPortable: C80DocTypecodes_PelvisXRayPortable,
  SpineLumbarXRaySingleView: C80DocTypecodes_SpineLumbarXRaySingleView,
  SpineLumbarXRaySingleViewPortable: C80DocTypecodes_SpineLumbarXRaySingleViewPortable,
  SpineLumbarXRay3Views: C80DocTypecodes_SpineLumbarXRay3Views,
  SpineLumbarXRay3ViewsPortable: C80DocTypecodes_SpineLumbarXRay3ViewsPortable,
  SpineLumbarXRayAPSingleView: C80DocTypecodes_SpineLumbarXRayAPSingleView,
  SpineLumbarXRayObliqueSingleView: C80DocTypecodes_SpineLumbarXRayObliqueSingleView,
  AnkleXRayAPAndLateral: C80DocTypecodes_AnkleXRayAPAndLateral,
  FingerSecondXRay: C80DocTypecodes_FingerSecondXRay,
  FingerThirdXRay: C80DocTypecodes_FingerThirdXRay,
  FingerFourthXRay: C80DocTypecodes_FingerFourthXRay,
  FingerFifthXRay: C80DocTypecodes_FingerFifthXRay,
  FootXRay2Views: C80DocTypecodes_FootXRay2Views,
  FootXRayWForcedDorsiflexion: C80DocTypecodes_FootXRayWForcedDorsiflexion,
  HipXRayLateralFrog: C80DocTypecodes_HipXRayLateralFrog,
  JointXRaySingleView: C80DocTypecodes_JointXRaySingleView,
  KneeXRay3Views: C80DocTypecodes_KneeXRay3Views,
  KneeXRay4Views: C80DocTypecodes_KneeXRay4Views,
  KneeXRayTunnel: C80DocTypecodes_KneeXRayTunnel,
  PatellaXRay: C80DocTypecodes_PatellaXRay,
  PatellaXRayPortable: C80DocTypecodes_PatellaXRayPortable,
  WristXRayAPAndLateral: C80DocTypecodes_WristXRayAPAndLateral,
  BreastMRI: C80DocTypecodes_BreastMRI,
  BreastBilateralMRI: C80DocTypecodes_BreastBilateralMRI,
  ElbowMRIWOContrast: C80DocTypecodes_ElbowMRIWOContrast,
  SpineLumbarXRay5Views: C80DocTypecodes_SpineLumbarXRay5Views,
  HeadCTWOContrast: C80DocTypecodes_HeadCTWOContrast,
  MRIGuidanceStereotacticForLocalizationInBrainWOContrast: C80DocTypecodes_MRIGuidanceStereotacticForLocalizationInBrainWOContrast,
  FacialBonesAndMaxillaCTWContrastIV: C80DocTypecodes_FacialBonesAndMaxillaCTWContrastIV,
  FacialBonesAndMaxillaCTWOContrast: C80DocTypecodes_FacialBonesAndMaxillaCTWOContrast,
  FacialBonesAndMaxillaCTWAndWOContrastIV: C80DocTypecodes_FacialBonesAndMaxillaCTWAndWOContrastIV,
  ChestVesselsCTAngiogramWAndWOContrastIV: C80DocTypecodes_ChestVesselsCTAngiogramWAndWOContrastIV,
  AbdominalVesselsCTAngiogramWAndWOContrastIV: C80DocTypecodes_AbdominalVesselsCTAngiogramWAndWOContrastIV,
  AortaAndFemoralArteryBilateralCTAngiogramWAndWOContrastIV: C80DocTypecodes_AortaAndFemoralArteryBilateralCTAngiogramWAndWOContrastIV,
  LowerExtremityVesselsCTAngiogramWAndWOContrastIV: C80DocTypecodes_LowerExtremityVesselsCTAngiogramWAndWOContrastIV,
  SpineCervicalAndThoracicAndLumbarFluoroscopyWContrastIT: C80DocTypecodes_SpineCervicalAndThoracicAndLumbarFluoroscopyWContrastIT,
  UpperGastrointestineAndSmallBowelFluoroscopyWContrastPO: C80DocTypecodes_UpperGastrointestineAndSmallBowelFluoroscopyWContrastPO,
  LacrimalDuctFluoroscopyWContrastIntraLacrimalDuct: C80DocTypecodes_LacrimalDuctFluoroscopyWContrastIntraLacrimalDuct,
  PosteriorFossaFluoroscopyWContrastIT: C80DocTypecodes_PosteriorFossaFluoroscopyWContrastIT,
  FluoroscopyGuidanceForInjectionOfSpineCervicalFacetJoint: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineCervicalFacetJoint,
  LungBilateralXRayWContrastIntrabronchial: C80DocTypecodes_LungBilateralXRayWContrastIntrabronchial,
  FluoroscopyGuidanceForInjectionOfSpineThoracicFacetJoint: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineThoracicFacetJoint,
  FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuctWContrastRetrograde: C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuctWContrastRetrograde,
  PeritoneumFluoroscopicAngiogramWContrastPercutaneous: C80DocTypecodes_PeritoneumFluoroscopicAngiogramWContrastPercutaneous,
  FluoroscopyGuidanceForInjectionOfSpineLumbarFacetJoint: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineLumbarFacetJoint,
  FluoroscopyGuidanceForCatheterizationOfFallopianTubesTranscervical: C80DocTypecodes_FluoroscopyGuidanceForCatheterizationOfFallopianTubesTranscervical,
  EpiduralVeinsFluoroscopicAngiogramWContrastIV: C80DocTypecodes_EpiduralVeinsFluoroscopicAngiogramWContrastIV,
  CarotidArteryExternalBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryExternalBilateralFluoroscopicAngiogramWContrastIA,
  CarotidArteryExternalFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryExternalFluoroscopicAngiogramWContrastIA,
  HeadArteryBilateralAndNeckArteryBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_HeadArteryBilateralAndNeckArteryBilateralFluoroscopicAngiogramWContrastIA,
  HeadArteryAndNeckArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_HeadArteryAndNeckArteryFluoroscopicAngiogramWContrastIA,
  IntercranialVesselAndNeckVesselFluoroscopicAngiogramWContrast: C80DocTypecodes_IntercranialVesselAndNeckVesselFluoroscopicAngiogramWContrast,
  OrbitVeinsFluoroscopicAngiogramWContrastIV: C80DocTypecodes_OrbitVeinsFluoroscopicAngiogramWContrastIV,
  SagittalSinusAndJugularVeinsFluoroscopicAngiogramWContrastIV: C80DocTypecodes_SagittalSinusAndJugularVeinsFluoroscopicAngiogramWContrastIV,
  SagittalSinusVeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_SagittalSinusVeinFluoroscopicAngiogramWContrastIV,
  BrachialArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_BrachialArteryFluoroscopicAngiogramWContrastIA,
  InternalMammaryArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_InternalMammaryArteryFluoroscopicAngiogramWContrastIA,
  PulmonaryArteryBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_PulmonaryArteryBilateralFluoroscopicAngiogramWContrastIA,
  AdrenalArteryBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AdrenalArteryBilateralFluoroscopicAngiogramWContrastIA,
  AdrenalArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AdrenalArteryFluoroscopicAngiogramWContrastIA,
  PelvisArteriesFluoroscopicAngiogramWContrastIA: C80DocTypecodes_PelvisArteriesFluoroscopicAngiogramWContrastIA,
  RenalArteryBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_RenalArteryBilateralFluoroscopicAngiogramWContrastIA,
  VisceralArteryFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_VisceralArteryFluoroscopicAngiogramAngioplastyWContrastIA,
  AortaAbdominalFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AortaAbdominalFluoroscopicAngiogramWContrastIA,
  AortaAndFemoralArteryBilateralFluoroscopicAngiogramRunoffWContrastIA: C80DocTypecodes_AortaAndFemoralArteryBilateralFluoroscopicAngiogramRunoffWContrastIA,
  LymphaticsAbdominalFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_LymphaticsAbdominalFluoroscopyWContrastIntraLymphatic,
  LymphaticsAbdominalBilateralFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_LymphaticsAbdominalBilateralFluoroscopyWContrastIntraLymphatic,
  PortalVeinFluoroscopicAngiogramWContrastTranshepatic: C80DocTypecodes_PortalVeinFluoroscopicAngiogramWContrastTranshepatic,
  PortalVeinFluoroscopicAngiogramWContrastTranshepaticAndWHemodynamics: C80DocTypecodes_PortalVeinFluoroscopicAngiogramWContrastTranshepaticAndWHemodynamics,
  AdrenalVeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_AdrenalVeinFluoroscopicAngiogramWContrastIV,
  AdrenalVeinBilateralFluoroscopicAngiogramWContrastIV: C80DocTypecodes_AdrenalVeinBilateralFluoroscopicAngiogramWContrastIV,
  InferiorVenaCavaFluoroscopicAngiogramWContrastIV: C80DocTypecodes_InferiorVenaCavaFluoroscopicAngiogramWContrastIV,
  RenalVeinBilateralFluoroscopicAngiogramWContrastIV: C80DocTypecodes_RenalVeinBilateralFluoroscopicAngiogramWContrastIV,
  RenalVeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_RenalVeinFluoroscopicAngiogramWContrastIV,
  ExtremityArteriesFluoroscopicAngiogramWContrastIA: C80DocTypecodes_ExtremityArteriesFluoroscopicAngiogramWContrastIA,
  ExtremityArteriesBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_ExtremityArteriesBilateralFluoroscopicAngiogramWContrastIA,
  ExtremityLymphaticsFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_ExtremityLymphaticsFluoroscopyWContrastIntraLymphatic,
  ExtremityLymphaticsBilateralFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_ExtremityLymphaticsBilateralFluoroscopyWContrastIntraLymphatic,
  PeripheralVeinsBilateralFluoroscopicAngiogramWContrastIV: C80DocTypecodes_PeripheralVeinsBilateralFluoroscopicAngiogramWContrastIV,
  BreastDuctUSWContrastIntraDuct: C80DocTypecodes_BreastDuctUSWContrastIntraDuct,
  SpineCervicalAndThoracicAndLumbarMRIWOContrast: C80DocTypecodes_SpineCervicalAndThoracicAndLumbarMRIWOContrast,
  SpineCervicalAndThoracicAndLumbarMRIWAndWOContrastIV: C80DocTypecodes_SpineCervicalAndThoracicAndLumbarMRIWAndWOContrastIV,
  HeadVesselsMRIAngiogram: C80DocTypecodes_HeadVesselsMRIAngiogram,
  NervesCranialMRI: C80DocTypecodes_NervesCranialMRI,
  HeadVeinsMRIAngiogram: C80DocTypecodes_HeadVeinsMRIAngiogram,
  CarotidVesselsAndNeckVesselsMRIAngiogram: C80DocTypecodes_CarotidVesselsAndNeckVesselsMRIAngiogram,
  NasopharynxMRI: C80DocTypecodes_NasopharynxMRI,
  AorticArchAndNeckVesselsMRIAngiogram: C80DocTypecodes_AorticArchAndNeckVesselsMRIAngiogram,
  ChestVesselsMRIAngiogram: C80DocTypecodes_ChestVesselsMRIAngiogram,
  AbdominalAortaAndArteriesMRIAngiogram: C80DocTypecodes_AbdominalAortaAndArteriesMRIAngiogram,
  AbdominalVeinsAndIVCMRIAngiogram: C80DocTypecodes_AbdominalVeinsAndIVCMRIAngiogram,
  CeliacVesselsAndSuperiorMesentericVesselsMRIAngiogram: C80DocTypecodes_CeliacVesselsAndSuperiorMesentericVesselsMRIAngiogram,
  LumbarPlexusMRI: C80DocTypecodes_LumbarPlexusMRI,
  PelvisVesselsMRIAngiogram: C80DocTypecodes_PelvisVesselsMRIAngiogram,
  RenalVesselsMRIAngiogram: C80DocTypecodes_RenalVesselsMRIAngiogram,
  LowerLegMRIWOContrast: C80DocTypecodes_LowerLegMRIWOContrast,
  LowerLegMRIWAndWOContrastIV: C80DocTypecodes_LowerLegMRIWAndWOContrastIV,
  FemoralVesselsMRIAngiogram: C80DocTypecodes_FemoralVesselsMRIAngiogram,
  FootVesselsMRIAngiogram: C80DocTypecodes_FootVesselsMRIAngiogram,
  ForearmVesselsMRIAngiogram: C80DocTypecodes_ForearmVesselsMRIAngiogram,
  LowerExtremityVesselsMRIAngiogram: C80DocTypecodes_LowerExtremityVesselsMRIAngiogram,
  UpperExtremityJointMRI: C80DocTypecodes_UpperExtremityJointMRI,
  ExtremityVeinsMRIAngiogram: C80DocTypecodes_ExtremityVeinsMRIAngiogram,
  KidneyBilateralAndRenalVesselsScan: C80DocTypecodes_KidneyBilateralAndRenalVesselsScan,
  USGuidanceForAspirationOfUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForAspirationOfUnspecifiedBodyRegion,
  HeadVesselsAndNeckVesselsUSDoppler: C80DocTypecodes_HeadVesselsAndNeckVesselsUSDoppler,
  LowerExtremityVeinUSDoppler: C80DocTypecodes_LowerExtremityVeinUSDoppler,
  UpperExtremityVeinUSDoppler: C80DocTypecodes_UpperExtremityVeinUSDoppler,
  CoccyxXRay: C80DocTypecodes_CoccyxXRay,
  SacrumXRay: C80DocTypecodes_SacrumXRay,
  PelvisSymphysisPubisXRay: C80DocTypecodes_PelvisSymphysisPubisXRay,
  RenalVesselsMRIAngiogramWContrastIV: C80DocTypecodes_RenalVesselsMRIAngiogramWContrastIV,
  TibioperonealVesselsMRIAngiogram: C80DocTypecodes_TibioperonealVesselsMRIAngiogram,
  TemporomandibularJointLeftXRay: C80DocTypecodes_TemporomandibularJointLeftXRay,
  TemporomandibularJointRightXRay: C80DocTypecodes_TemporomandibularJointRightXRay,
  CervicocerebralArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CervicocerebralArteryFluoroscopicAngiogramWContrastIA,
  FluoroscopyGuidanceForCatheterizationOfBiliaryDuctsAndPancreaticDuctWContrastRetrograde: C80DocTypecodes_FluoroscopyGuidanceForCatheterizationOfBiliaryDuctsAndPancreaticDuctWContrastRetrograde,
  NonGynecologicalCytologyMethodStudy: C80DocTypecodes_NonGynecologicalCytologyMethodStudy,
  CytologyCervicalOrVaginalSmearOrScrapingStudy: C80DocTypecodes_CytologyCervicalOrVaginalSmearOrScrapingStudy,
  CytologyReportOfTissueFineNeedleAspirateCytoStain: C80DocTypecodes_CytologyReportOfTissueFineNeedleAspirateCytoStain,
  FlowCytometryStudy: C80DocTypecodes_FlowCytometryStudy,
  BloodBankConsult: C80DocTypecodes_BloodBankConsult,
  BoneMarrowPathologyBiopsyReport: C80DocTypecodes_BoneMarrowPathologyBiopsyReport,
  FDAPackageInsertBoxedWarningSection: C80DocTypecodes_FDAPackageInsertBoxedWarningSection,
  FDAPackageInsertIndicationsAndUsageSection: C80DocTypecodes_FDAPackageInsertIndicationsAndUsageSection,
  FDAPackageInsertDosageAndAdministrationSection: C80DocTypecodes_FDAPackageInsertDosageAndAdministrationSection,
  FDAPackageInsertHowSuppliedSection: C80DocTypecodes_FDAPackageInsertHowSuppliedSection,
  FDAPackageInsertContraindicationsSection: C80DocTypecodes_FDAPackageInsertContraindicationsSection,
  FDAPackageInsertWarningsSection: C80DocTypecodes_FDAPackageInsertWarningsSection,
  FDAPackageInsertGeneralPrecautionsSection: C80DocTypecodes_FDAPackageInsertGeneralPrecautionsSection,
  FDAPackageInsertDrugInteractionsSection: C80DocTypecodes_FDAPackageInsertDrugInteractionsSection,
  FDAPackageInsertDrugLaboratoryTestInteractionsSection: C80DocTypecodes_FDAPackageInsertDrugLaboratoryTestInteractionsSection,
  FDAPackageInsertLaboratoryTestsSection: C80DocTypecodes_FDAPackageInsertLaboratoryTestsSection,
  FDAPackageInsertInformationForPatientsSection: C80DocTypecodes_FDAPackageInsertInformationForPatientsSection,
  FDAPackageInsertTeratogenicEffectsSection: C80DocTypecodes_FDAPackageInsertTeratogenicEffectsSection,
  FDAPackageInsertNonteratogenicEffectsSection: C80DocTypecodes_FDAPackageInsertNonteratogenicEffectsSection,
  FDAPackageInsertLaborAndDeliverySection: C80DocTypecodes_FDAPackageInsertLaborAndDeliverySection,
  FDAPackageInsertNursingMothersSection: C80DocTypecodes_FDAPackageInsertNursingMothersSection,
  FDAPackageInsertPediatricUseSection: C80DocTypecodes_FDAPackageInsertPediatricUseSection,
  FDAPackageInsertGeriatricUseSection: C80DocTypecodes_FDAPackageInsertGeriatricUseSection,
  FDAPackageInsertCarcinogenesisAndMutagenesisAndImpairmentOfFertilitySection: C80DocTypecodes_FDAPackageInsertCarcinogenesisAndMutagenesisAndImpairmentOfFertilitySection,
  FDAPackageInsertAdverseReactionsSection: C80DocTypecodes_FDAPackageInsertAdverseReactionsSection,
  FDAPackageInsertControlledSubstanceSection: C80DocTypecodes_FDAPackageInsertControlledSubstanceSection,
  FDAPackageInsertAbuseSection: C80DocTypecodes_FDAPackageInsertAbuseSection,
  FDAPackageInsertDependenceSection: C80DocTypecodes_FDAPackageInsertDependenceSection,
  FDAPackageInsertOverdosageSection: C80DocTypecodes_FDAPackageInsertOverdosageSection,
  FDAPackageInsertDescriptionSection: C80DocTypecodes_FDAPackageInsertDescriptionSection,
  FDAPackageInsertClinicalPharmacologySection: C80DocTypecodes_FDAPackageInsertClinicalPharmacologySection,
  FDAPackageInsertAnimalPharmacologyToxicologySection: C80DocTypecodes_FDAPackageInsertAnimalPharmacologyToxicologySection,
  FDAPackageInsertClinicalStudiesSection: C80DocTypecodes_FDAPackageInsertClinicalStudiesSection,
  FDAPackageInsertReferencesSection: C80DocTypecodes_FDAPackageInsertReferencesSection,
  CardiologyHospitalAdmissionHistoryAndPhysicalNote: C80DocTypecodes_CardiologyHospitalAdmissionHistoryAndPhysicalNote,
  ComprehensiveHistoryAndPhysicalNote: C80DocTypecodes_ComprehensiveHistoryAndPhysicalNote,
  NursingFacilityComprehensiveHistoryAndPhysicalNote: C80DocTypecodes_NursingFacilityComprehensiveHistoryAndPhysicalNote,
  NursingFacilityConferenceNote: C80DocTypecodes_NursingFacilityConferenceNote,
  ConferenceNote: C80DocTypecodes_ConferenceNote,
  CardiologyConsultNote: C80DocTypecodes_CardiologyConsultNote,
  IntensiveCareUnitConsultNote: C80DocTypecodes_IntensiveCareUnitConsultNote,
  GeneralMedicineOutpatientConsultNote: C80DocTypecodes_GeneralMedicineOutpatientConsultNote,
  PsychiatryHospitalConsultNote: C80DocTypecodes_PsychiatryHospitalConsultNote,
  PulmonaryConsultNote: C80DocTypecodes_PulmonaryConsultNote,
  HospitalConsultNote: C80DocTypecodes_HospitalConsultNote,
  HospitalDischargeSummary: C80DocTypecodes_HospitalDischargeSummary,
  PhysicianHospitalDischargeSummary: C80DocTypecodes_PhysicianHospitalDischargeSummary,
  PatientQuoteSHomeEducationNote: C80DocTypecodes_PatientQuoteSHomeEducationNote,
  OutpatientNote: C80DocTypecodes_OutpatientNote,
  Note: C80DocTypecodes_Note,
  DiabetologyOutpatientNote: C80DocTypecodes_DiabetologyOutpatientNote,
  EmergencyDepartmentNote: C80DocTypecodes_EmergencyDepartmentNote,
  HospitalNote: C80DocTypecodes_HospitalNote,
  NursingFacilityNote: C80DocTypecodes_NursingFacilityNote,
  HospitalGroupCounselingNote: C80DocTypecodes_HospitalGroupCounselingNote,
  MedicalStudentHospitalHistoryAndPhysicalNote: C80DocTypecodes_MedicalStudentHospitalHistoryAndPhysicalNote,
  PhysicianNursingFacilityHistoryAndPhysicalNote: C80DocTypecodes_PhysicianNursingFacilityHistoryAndPhysicalNote,
  HistoryAndPhysicalNote: C80DocTypecodes_HistoryAndPhysicalNote,
  PatientQuoteSHomeInitialAssessmentNote: C80DocTypecodes_PatientQuoteSHomeInitialAssessmentNote,
  NursingFacilityInitialAssessmentNote: C80DocTypecodes_NursingFacilityInitialAssessmentNote,
  OutpatientInitialAssessmentNote: C80DocTypecodes_OutpatientInitialAssessmentNote,
  InterventionalProcedureNote: C80DocTypecodes_InterventionalProcedureNote,
  PathologyProcedureNote: C80DocTypecodes_PathologyProcedureNote,
  AnesthesiologyHospitalPreoperativeEvaluationAndManagementNote: C80DocTypecodes_AnesthesiologyHospitalPreoperativeEvaluationAndManagementNote,
  CardiologyOutpatientProgressNote: C80DocTypecodes_CardiologyOutpatientProgressNote,
  CaseManagerPatientQuoteSHomeProgressNote: C80DocTypecodes_CaseManagerPatientQuoteSHomeProgressNote,
  IntensiveCareUnitProgressNote: C80DocTypecodes_IntensiveCareUnitProgressNote,
  DentistryHygienistOutpatientProgressNote: C80DocTypecodes_DentistryHygienistOutpatientProgressNote,
  DentistryOutpatientProgressNote: C80DocTypecodes_DentistryOutpatientProgressNote,
  PatientQuoteSHomeProgressNote: C80DocTypecodes_PatientQuoteSHomeProgressNote,
  HospitalProgressNote: C80DocTypecodes_HospitalProgressNote,
  OutpatientProgressNote: C80DocTypecodes_OutpatientProgressNote,
  PharmacyOutpatientProgressNote: C80DocTypecodes_PharmacyOutpatientProgressNote,
  SummaryOfEpisodeNote: C80DocTypecodes_SummaryOfEpisodeNote,
  PhysicianAttendingOutpatientSupervisoryNote: C80DocTypecodes_PhysicianAttendingOutpatientSupervisoryNote,
  CardiologyPhysicianAttendingOutpatientSupervisoryNote: C80DocTypecodes_CardiologyPhysicianAttendingOutpatientSupervisoryNote,
  GastroenterologyPhysicianAttendingOutpatientSupervisoryNote: C80DocTypecodes_GastroenterologyPhysicianAttendingOutpatientSupervisoryNote,
  OutpatientSurgicalOperationNote: C80DocTypecodes_OutpatientSurgicalOperationNote,
  TargetedHistoryAndPhysicalNote: C80DocTypecodes_TargetedHistoryAndPhysicalNote,
  NurseTelephoneEncounterNote: C80DocTypecodes_NurseTelephoneEncounterNote,
  NurseAdmissionEvaluationNote: C80DocTypecodes_NurseAdmissionEvaluationNote,
  NurseDischargeSummary: C80DocTypecodes_NurseDischargeSummary,
  NurseNote: C80DocTypecodes_NurseNote,
  NursePreoperativeEvaluationAndManagementNote: C80DocTypecodes_NursePreoperativeEvaluationAndManagementNote,
  TelephoneEncounterNote: C80DocTypecodes_TelephoneEncounterNote,
  AnesthesiologyOutpatientConsultNote: C80DocTypecodes_AnesthesiologyOutpatientConsultNote,
  AnesthesiologyNote: C80DocTypecodes_AnesthesiologyNote,
  AnesthesiologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_AnesthesiologyPreoperativeEvaluationAndManagementNote,
  CardiologyNote: C80DocTypecodes_CardiologyNote,
  CardiologyOutpatientNote: C80DocTypecodes_CardiologyOutpatientNote,
  CriticalCareMedicineNote: C80DocTypecodes_CriticalCareMedicineNote,
  CriticalCareMedicineTransferSummaryNote: C80DocTypecodes_CriticalCareMedicineTransferSummaryNote,
  DentistryConsultNote: C80DocTypecodes_DentistryConsultNote,
  DermatologyConsultNote: C80DocTypecodes_DermatologyConsultNote,
  DermatologyNote: C80DocTypecodes_DermatologyNote,
  DiabetologyConsultNote: C80DocTypecodes_DiabetologyConsultNote,
  GastroenterologyConsultNote: C80DocTypecodes_GastroenterologyConsultNote,
  GastroenterologyNote: C80DocTypecodes_GastroenterologyNote,
  GeneralMedicineAdmissionHistoryAndPhysicalNote: C80DocTypecodes_GeneralMedicineAdmissionHistoryAndPhysicalNote,
  GeneralMedicineConsultNote: C80DocTypecodes_GeneralMedicineConsultNote,
  GeneralMedicineNote: C80DocTypecodes_GeneralMedicineNote,
  GeneralMedicineOutpatientNote: C80DocTypecodes_GeneralMedicineOutpatientNote,
  GeneralMedicineMedicalStudentNote: C80DocTypecodes_GeneralMedicineMedicalStudentNote,
  GeneralMedicineNurseNote: C80DocTypecodes_GeneralMedicineNurseNote,
  GeneralMedicinePhysicianAttendingNote: C80DocTypecodes_GeneralMedicinePhysicianAttendingNote,
  GeneralMedicineTransferSummaryNote: C80DocTypecodes_GeneralMedicineTransferSummaryNote,
  SurgeryPhysicianAttendingNote: C80DocTypecodes_SurgeryPhysicianAttendingNote,
  SurgeryHistoryAndPhysicalNote: C80DocTypecodes_SurgeryHistoryAndPhysicalNote,
  GeriatricMedicineConsultNote: C80DocTypecodes_GeriatricMedicineConsultNote,
  ObstetricsAndGynecologyConsultNote: C80DocTypecodes_ObstetricsAndGynecologyConsultNote,
  ObstetricsAndGynecologyNote: C80DocTypecodes_ObstetricsAndGynecologyNote,
  HematologyPlusMedicalOncologyConsultNote: C80DocTypecodes_HematologyPlusMedicalOncologyConsultNote,
  HematologyPlusMedicalOncologyNote: C80DocTypecodes_HematologyPlusMedicalOncologyNote,
  InfectiousDiseaseConsultNote: C80DocTypecodes_InfectiousDiseaseConsultNote,
  InfectiousDiseaseNote: C80DocTypecodes_InfectiousDiseaseNote,
  KinesiotherapyConsultNote: C80DocTypecodes_KinesiotherapyConsultNote,
  KinesiotherapyNote: C80DocTypecodes_KinesiotherapyNote,
  MentalHealthConsultNote: C80DocTypecodes_MentalHealthConsultNote,
  MentalHealthNote: C80DocTypecodes_MentalHealthNote,
  MentalHealthGroupCounselingNote: C80DocTypecodes_MentalHealthGroupCounselingNote,
  PsychiatryConsultNote: C80DocTypecodes_PsychiatryConsultNote,
  PsychiatryGroupCounselingNote: C80DocTypecodes_PsychiatryGroupCounselingNote,
  PsychologyConsultNote: C80DocTypecodes_PsychologyConsultNote,
  PsychologyNote: C80DocTypecodes_PsychologyNote,
  PsychologyGroupCounselingNote: C80DocTypecodes_PsychologyGroupCounselingNote,
  InterdisciplinaryNote: C80DocTypecodes_InterdisciplinaryNote,
  NephrologyConsultNote: C80DocTypecodes_NephrologyConsultNote,
  NephrologyNote: C80DocTypecodes_NephrologyNote,
  NeurologyConsultNote: C80DocTypecodes_NeurologyConsultNote,
  NeurologicalSurgeryConsultNote: C80DocTypecodes_NeurologicalSurgeryConsultNote,
  NeurologicalSurgeryNote: C80DocTypecodes_NeurologicalSurgeryNote,
  NutritionAndDieteticsConsultNote: C80DocTypecodes_NutritionAndDieteticsConsultNote,
  NutritionAndDieteticsNote: C80DocTypecodes_NutritionAndDieteticsNote,
  OccupationalMedicineNote: C80DocTypecodes_OccupationalMedicineNote,
  OccupationalMedicineConsultNote: C80DocTypecodes_OccupationalMedicineConsultNote,
  OncologyConsultNote: C80DocTypecodes_OncologyConsultNote,
  OncologyNote: C80DocTypecodes_OncologyNote,
  OphthalmologyConsultNote: C80DocTypecodes_OphthalmologyConsultNote,
  OphthalmologyNote: C80DocTypecodes_OphthalmologyNote,
  OphthalmologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_OphthalmologyPreoperativeEvaluationAndManagementNote,
  OptometryConsultNote: C80DocTypecodes_OptometryConsultNote,
  OptometryNote: C80DocTypecodes_OptometryNote,
  OralAndMaxillofacialSurgeryConsultNote: C80DocTypecodes_OralAndMaxillofacialSurgeryConsultNote,
  OralAndMaxillofacialSurgeryNote: C80DocTypecodes_OralAndMaxillofacialSurgeryNote,
  OrthopaedicSurgeryConsultNote: C80DocTypecodes_OrthopaedicSurgeryConsultNote,
  OrthopaedicSurgeryNote: C80DocTypecodes_OrthopaedicSurgeryNote,
  OtolaryngologyConsultNote: C80DocTypecodes_OtolaryngologyConsultNote,
  OtolaryngologyNote: C80DocTypecodes_OtolaryngologyNote,
  OtolaryngologySurgicalOperationNote: C80DocTypecodes_OtolaryngologySurgicalOperationNote,
  PathologyEvaluationAndManagementNote: C80DocTypecodes_PathologyEvaluationAndManagementNote,
  PharmacyConsultNote: C80DocTypecodes_PharmacyConsultNote,
  PharmacyNote: C80DocTypecodes_PharmacyNote,
  PhysicalMedicineAndRehabilitationConsultNote: C80DocTypecodes_PhysicalMedicineAndRehabilitationConsultNote,
  PhysicalMedicineAndRehabilitationNote: C80DocTypecodes_PhysicalMedicineAndRehabilitationNote,
  PhysicalTherapyConsultNote: C80DocTypecodes_PhysicalTherapyConsultNote,
  PlasticSurgeryConsultNote: C80DocTypecodes_PlasticSurgeryConsultNote,
  PlasticSurgeryNote: C80DocTypecodes_PlasticSurgeryNote,
  PodiatryConsultNote: C80DocTypecodes_PodiatryConsultNote,
  PodiatryNote: C80DocTypecodes_PodiatryNote,
  PulmonaryNote: C80DocTypecodes_PulmonaryNote,
  RadiationOncologyConsultNote: C80DocTypecodes_RadiationOncologyConsultNote,
  RadiationOncologyNote: C80DocTypecodes_RadiationOncologyNote,
  RecreationalTherapyConsultNote: C80DocTypecodes_RecreationalTherapyConsultNote,
  RecreationalTherapyNote: C80DocTypecodes_RecreationalTherapyNote,
  RespiratoryTherapyConsultNote: C80DocTypecodes_RespiratoryTherapyConsultNote,
  RespiratoryTherapyNote: C80DocTypecodes_RespiratoryTherapyNote,
  RheumatologyConsultNote: C80DocTypecodes_RheumatologyConsultNote,
  RheumatologyNote: C80DocTypecodes_RheumatologyNote,
  SocialWorkConsultNote: C80DocTypecodes_SocialWorkConsultNote,
  SocialWorkGroupCounselingNote: C80DocTypecodes_SocialWorkGroupCounselingNote,
  SocialWorkTelephoneEncounterNote: C80DocTypecodes_SocialWorkTelephoneEncounterNote,
  SpeechLanguagePathologyPlusAudiologyConsultNote: C80DocTypecodes_SpeechLanguagePathologyPlusAudiologyConsultNote,
  SpeechLanguagePathologyPlusAudiologyNote: C80DocTypecodes_SpeechLanguagePathologyPlusAudiologyNote,
  SurgeryConsultNote: C80DocTypecodes_SurgeryConsultNote,
  SurgeryNote: C80DocTypecodes_SurgeryNote,
  ThoracicSurgeryConsultNote: C80DocTypecodes_ThoracicSurgeryConsultNote,
  ThoracicSurgeryOutpatientNote: C80DocTypecodes_ThoracicSurgeryOutpatientNote,
  UrologyConsultNote: C80DocTypecodes_UrologyConsultNote,
  UrologyNote: C80DocTypecodes_UrologyNote,
  VascularSurgeryConsultNote: C80DocTypecodes_VascularSurgeryConsultNote,
  VascularSurgeryOutpatientNote: C80DocTypecodes_VascularSurgeryOutpatientNote,
  OccupationalTherapyConsultNote: C80DocTypecodes_OccupationalTherapyConsultNote,
  EvaluationAndManagementOfAnticoagulationNote: C80DocTypecodes_EvaluationAndManagementOfAnticoagulationNote,
  EvaluationAndManagementOfSubstanceAbuseNote: C80DocTypecodes_EvaluationAndManagementOfSubstanceAbuseNote,
  PainMedicineNote: C80DocTypecodes_PainMedicineNote,
  EvaluationAndManagementOfHyperlipidemia: C80DocTypecodes_EvaluationAndManagementOfHyperlipidemia,
  EvaluationAndManagementOfHypertension: C80DocTypecodes_EvaluationAndManagementOfHypertension,
  DiabetologyNote: C80DocTypecodes_DiabetologyNote,
  GeneralMedicinePhysicianAttendingHospitalAdmissionEvaluationNote: C80DocTypecodes_GeneralMedicinePhysicianAttendingHospitalAdmissionEvaluationNote,
  MentalHealthCounselingNote: C80DocTypecodes_MentalHealthCounselingNote,
  PsychiatryCounselingNote: C80DocTypecodes_PsychiatryCounselingNote,
  PsychologyCounselingNote: C80DocTypecodes_PsychologyCounselingNote,
  OphthalmologyOutpatientPostoperativeEvaluationAndManagementNote: C80DocTypecodes_OphthalmologyOutpatientPostoperativeEvaluationAndManagementNote,
  OrthopaedicSurgerySurgicalOperationNote: C80DocTypecodes_OrthopaedicSurgerySurgicalOperationNote,
  PharmacyCounselingNote: C80DocTypecodes_PharmacyCounselingNote,
  PlasticSurgerySurgicalOperationNote: C80DocTypecodes_PlasticSurgerySurgicalOperationNote,
  SocialWorkCounselingNote: C80DocTypecodes_SocialWorkCounselingNote,
  SurgeryAdmissionEvaluationNote: C80DocTypecodes_SurgeryAdmissionEvaluationNote,
  SurgerySurgicalOperationNote: C80DocTypecodes_SurgerySurgicalOperationNote,
  SurgeryPostoperativeEvaluationAndManagementNote: C80DocTypecodes_SurgeryPostoperativeEvaluationAndManagementNote,
  SurgeryPreoperativeEvaluationAndManagementNote: C80DocTypecodes_SurgeryPreoperativeEvaluationAndManagementNote,
  UrologySurgicalOperationNote: C80DocTypecodes_UrologySurgicalOperationNote,
  EmergencyMedicineNote: C80DocTypecodes_EmergencyMedicineNote,
  EndocrinologyConsultNote: C80DocTypecodes_EndocrinologyConsultNote,
  SurgeryNursePostoperativeEvaluationAndManagementNote: C80DocTypecodes_SurgeryNursePostoperativeEvaluationAndManagementNote,
  SurgeryNursePreoperativeEvaluationAndManagementNote: C80DocTypecodes_SurgeryNursePreoperativeEvaluationAndManagementNote,
  EducationNote: C80DocTypecodes_EducationNote,
  CardiologyInterventionalProcedureNote: C80DocTypecodes_CardiologyInterventionalProcedureNote,
  DiabetologyEducationNote: C80DocTypecodes_DiabetologyEducationNote,
  EndocrinologyNote: C80DocTypecodes_EndocrinologyNote,
  GastroenterologyInterventionalProcedureNote: C80DocTypecodes_GastroenterologyInterventionalProcedureNote,
  GeneralMedicineProgressNote: C80DocTypecodes_GeneralMedicineProgressNote,
  GeneralMedicineOutpatientProgressNote: C80DocTypecodes_GeneralMedicineOutpatientProgressNote,
  GeriatricMedicineOutpatientEducationNote: C80DocTypecodes_GeriatricMedicineOutpatientEducationNote,
  MentalHealthProgressNote: C80DocTypecodes_MentalHealthProgressNote,
  NeurologyNote: C80DocTypecodes_NeurologyNote,
  PastoralCareNote: C80DocTypecodes_PastoralCareNote,
  ClinicalTrialProtocolGeneralInformationSection: C80DocTypecodes_ClinicalTrialProtocolGeneralInformationSection,
  ClinicalTrialProtocolBackgroundInformationSection: C80DocTypecodes_ClinicalTrialProtocolBackgroundInformationSection,
  ClinicalTrialProtocolTrialObjectivesAndPurposeSection: C80DocTypecodes_ClinicalTrialProtocolTrialObjectivesAndPurposeSection,
  ClinicalTrialProtocolTrialDesignSection: C80DocTypecodes_ClinicalTrialProtocolTrialDesignSection,
  ClinicalTrialProtocolSubjectSelectionAndWithdrawalSection: C80DocTypecodes_ClinicalTrialProtocolSubjectSelectionAndWithdrawalSection,
  ClinicalTrialProtocolSubjectParticipationPlusEpochsSection: C80DocTypecodes_ClinicalTrialProtocolSubjectParticipationPlusEpochsSection,
  ClinicalTrialProtocolTreatmentOfSubjectsPlusEpochsSection: C80DocTypecodes_ClinicalTrialProtocolTreatmentOfSubjectsPlusEpochsSection,
  ClinicalTrialProtocolAssessmentSection: C80DocTypecodes_ClinicalTrialProtocolAssessmentSection,
  ClinicalTrialProtocolEfficacyAssessmentSection: C80DocTypecodes_ClinicalTrialProtocolEfficacyAssessmentSection,
  ClinicalTrialProtocolAssessmentOfSafetySection: C80DocTypecodes_ClinicalTrialProtocolAssessmentOfSafetySection,
  ClinicalTrialProtocolStatisticsSection: C80DocTypecodes_ClinicalTrialProtocolStatisticsSection,
  ClinicalTrialProtocolDirectAccessToSourceDataPlusDocumentsSection: C80DocTypecodes_ClinicalTrialProtocolDirectAccessToSourceDataPlusDocumentsSection,
  ClinicalTrialProtocolQualityControlAndQualityAssuranceSection: C80DocTypecodes_ClinicalTrialProtocolQualityControlAndQualityAssuranceSection,
  ClinicalTrialProtocolEthicsSection: C80DocTypecodes_ClinicalTrialProtocolEthicsSection,
  ClinicalTrialProtocolDataHandlingAndRecordKeepingSection: C80DocTypecodes_ClinicalTrialProtocolDataHandlingAndRecordKeepingSection,
  ClinicalTrialProtocolFinancingAndInsuranceSection: C80DocTypecodes_ClinicalTrialProtocolFinancingAndInsuranceSection,
  ClinicalTrialProtocolPublicationPolicySection: C80DocTypecodes_ClinicalTrialProtocolPublicationPolicySection,
  ClinicalTrialProtocolSupplementsSection: C80DocTypecodes_ClinicalTrialProtocolSupplementsSection,
  ClinicalTrialProtocolClinicalTrialProtocol: C80DocTypecodes_ClinicalTrialProtocolClinicalTrialProtocol,
  ExtremityArteryFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_ExtremityArteryFluoroscopicAngiogramAngioplastyWContrastIA,
  InferiorVenaCavaFluoroscopicAngiogramAngioplastyWContrastIV: C80DocTypecodes_InferiorVenaCavaFluoroscopicAngiogramAngioplastyWContrastIV,
  AortaFluoroscopicAngiogramAtherectomyWContrastIA: C80DocTypecodes_AortaFluoroscopicAngiogramAtherectomyWContrastIA,
  CTGuidanceForAbscessDrainageOfAbdomen: C80DocTypecodes_CTGuidanceForAbscessDrainageOfAbdomen,
  FluoroscopyGuidanceForAbscessDrainageOfUnspecifiedBodyRegion: C80DocTypecodes_FluoroscopyGuidanceForAbscessDrainageOfUnspecifiedBodyRegion,
  CTGuidanceForAspirationOfBreast: C80DocTypecodes_CTGuidanceForAspirationOfBreast,
  CTGuidanceForAspirationOfCystOfUnspecifiedBodyRegion: C80DocTypecodes_CTGuidanceForAspirationOfCystOfUnspecifiedBodyRegion,
  FluoroscopyGuidanceForAspirationOfHip: C80DocTypecodes_FluoroscopyGuidanceForAspirationOfHip,
  FluoroscopyGuidanceForBronchoscopyOfChest: C80DocTypecodes_FluoroscopyGuidanceForBronchoscopyOfChest,
  FluoroscopyGuidanceForBiopsyOfAbdomen: C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfAbdomen,
  CTGuidanceForBiopsyOfBone: C80DocTypecodes_CTGuidanceForBiopsyOfBone,
  CTGuidanceForBiopsyOfHead: C80DocTypecodes_CTGuidanceForBiopsyOfHead,
  CTGuidanceForBiopsyOfBreast: C80DocTypecodes_CTGuidanceForBiopsyOfBreast,
  FluoroscopyGuidanceForBiopsyOfChest: C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfChest,
  CTGuidanceForBiopsyOfChest: C80DocTypecodes_CTGuidanceForBiopsyOfChest,
  CTGuidanceForBiopsyOfLowerExtremity: C80DocTypecodes_CTGuidanceForBiopsyOfLowerExtremity,
  CTGuidanceForBiopsyOfUpperExtremity: C80DocTypecodes_CTGuidanceForBiopsyOfUpperExtremity,
  CTGuidanceForBiopsyOfSalivaryGland: C80DocTypecodes_CTGuidanceForBiopsyOfSalivaryGland,
  FluoroscopyGuidanceForBiopsyOfKidney: C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfKidney,
  FluoroscopyGuidanceForBiopsyOfLiver: C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfLiver,
  CTGuidanceForBiopsyOfLymphNode: C80DocTypecodes_CTGuidanceForBiopsyOfLymphNode,
  FluoroscopyGuidanceForBiopsyOfPancreas: C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfPancreas,
  CTGuidanceForBiopsyOfProstate: C80DocTypecodes_CTGuidanceForBiopsyOfProstate,
  CTGuidanceForBiopsyOfSpineCervical: C80DocTypecodes_CTGuidanceForBiopsyOfSpineCervical,
  CTGuidanceForBiopsyOfSpineLumbar: C80DocTypecodes_CTGuidanceForBiopsyOfSpineLumbar,
  CTGuidanceForBiopsyOfSpineThoracic: C80DocTypecodes_CTGuidanceForBiopsyOfSpineThoracic,
  FluoroscopyGuidanceForBiopsyOfSpleen: C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfSpleen,
  CTGuidanceForBiopsyOfThyroid: C80DocTypecodes_CTGuidanceForBiopsyOfThyroid,
  CTGuidanceForBiopsyOfChestWContrastIV: C80DocTypecodes_CTGuidanceForBiopsyOfChestWContrastIV,
  CTGuidanceForBiopsyOfChestWAndWOContrastIV: C80DocTypecodes_CTGuidanceForBiopsyOfChestWAndWOContrastIV,
  CTGuidanceForBiopsyOfChestWOContrast: C80DocTypecodes_CTGuidanceForBiopsyOfChestWOContrast,
  FluoroscopyGuidanceForPlacementOfCatheterInUnspecifiedBodyRegion: C80DocTypecodes_FluoroscopyGuidanceForPlacementOfCatheterInUnspecifiedBodyRegion,
  CTGuidanceForDrainageOfAbdomen: C80DocTypecodes_CTGuidanceForDrainageOfAbdomen,
  CTGuidanceForDrainageOfAnus: C80DocTypecodes_CTGuidanceForDrainageOfAnus,
  CTGuidanceForDrainageOfAppendix: C80DocTypecodes_CTGuidanceForDrainageOfAppendix,
  CTGuidanceForDrainageOfChest: C80DocTypecodes_CTGuidanceForDrainageOfChest,
  CTGuidanceForDrainageOfGallbladder: C80DocTypecodes_CTGuidanceForDrainageOfGallbladder,
  CTGuidanceForDrainageOfKidney: C80DocTypecodes_CTGuidanceForDrainageOfKidney,
  CTGuidanceForDrainageOfLiver: C80DocTypecodes_CTGuidanceForDrainageOfLiver,
  CTGuidanceForDrainageOfLymphNode: C80DocTypecodes_CTGuidanceForDrainageOfLymphNode,
  CTGuidanceForDrainageOfPelvis: C80DocTypecodes_CTGuidanceForDrainageOfPelvis,
  CTGuidanceForDrainageOfUnspecifiedBodyRegion: C80DocTypecodes_CTGuidanceForDrainageOfUnspecifiedBodyRegion,
  CTGuidanceForDrainageOfChestWContrastIV: C80DocTypecodes_CTGuidanceForDrainageOfChestWContrastIV,
  CTGuidanceForDrainageOfChestWOContrast: C80DocTypecodes_CTGuidanceForDrainageOfChestWOContrast,
  FluoroscopyGuidanceForEndoscopyOfStomach: C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfStomach,
  FluoroscopyGuidanceForGastrostomyOfStomach: C80DocTypecodes_FluoroscopyGuidanceForGastrostomyOfStomach,
  FluoroscopyGuidanceForInjectionOfSacroiliacJoint: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSacroiliacJoint,
  CTGuidanceForLocalizationOfBreastLeft: C80DocTypecodes_CTGuidanceForLocalizationOfBreastLeft,
  CTGuidanceForLocalizationOfBreastRight: C80DocTypecodes_CTGuidanceForLocalizationOfBreastRight,
  CTGuidanceForNerveBlockOfAbdomen: C80DocTypecodes_CTGuidanceForNerveBlockOfAbdomen,
  CTGuidanceForNerveBlockOfPelvis: C80DocTypecodes_CTGuidanceForNerveBlockOfPelvis,
  CTGuidanceForNerveBlockOfSpineLumbar: C80DocTypecodes_CTGuidanceForNerveBlockOfSpineLumbar,
  CTGuidanceForPercutaneousVertebroplastyOfSpine: C80DocTypecodes_CTGuidanceForPercutaneousVertebroplastyOfSpine,
  CTGuidanceForPercutaneousVertebroplastyOfSpineLumbar: C80DocTypecodes_CTGuidanceForPercutaneousVertebroplastyOfSpineLumbar,
  CTGuidanceForPercutaneousVertebroplastyOfSpineThoracic: C80DocTypecodes_CTGuidanceForPercutaneousVertebroplastyOfSpineThoracic,
  FluoroscopyGuidanceForPercutaneousVertebroplastyOfSpine: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousVertebroplastyOfSpine,
  CTGuidanceForPlacementOfRadiationTherapyFieldsInUnspecifiedBodyRegion: C80DocTypecodes_CTGuidanceForPlacementOfRadiationTherapyFieldsInUnspecifiedBodyRegion,
  CTGuidanceForPlacementOfTubeInChest: C80DocTypecodes_CTGuidanceForPlacementOfTubeInChest,
  AnkleXRayTomograph: C80DocTypecodes_AnkleXRayTomograph,
  AnkleCT: C80DocTypecodes_AnkleCT,
  AnkleBilateralCT: C80DocTypecodes_AnkleBilateralCT,
  AnkleLeftCT: C80DocTypecodes_AnkleLeftCT,
  AnkleLeftXRayTomograph: C80DocTypecodes_AnkleLeftXRayTomograph,
  AnkleRightCT: C80DocTypecodes_AnkleRightCT,
  AortaCT: C80DocTypecodes_AortaCT,
  AortaMRIAngiogram: C80DocTypecodes_AortaMRIAngiogram,
  AortaMRI: C80DocTypecodes_AortaMRI,
  AortaAbdominalCT: C80DocTypecodes_AortaAbdominalCT,
  AortaAbdominalMRI: C80DocTypecodes_AortaAbdominalMRI,
  AortaThoracicMRI: C80DocTypecodes_AortaThoracicMRI,
  AorticArchMRIAngiogram: C80DocTypecodes_AorticArchMRIAngiogram,
  AppendixCT: C80DocTypecodes_AppendixCT,
  FaceMRI: C80DocTypecodes_FaceMRI,
  BreastLeftMRI: C80DocTypecodes_BreastLeftMRI,
  BreastRightMRI: C80DocTypecodes_BreastRightMRI,
  InternalAuditoryCanalMRI: C80DocTypecodes_InternalAuditoryCanalMRI,
  InternalAuditoryCanalLeftCT: C80DocTypecodes_InternalAuditoryCanalLeftCT,
  InternalAuditoryCanalCT: C80DocTypecodes_InternalAuditoryCanalCT,
  ClavicleXRayTomograph: C80DocTypecodes_ClavicleXRayTomograph,
  ClavicleCT: C80DocTypecodes_ClavicleCT,
  ClavicleMRI: C80DocTypecodes_ClavicleMRI,
  ElbowCT: C80DocTypecodes_ElbowCT,
  ElbowXRayTomograph: C80DocTypecodes_ElbowXRayTomograph,
  ElbowBilateralXRayTomograph: C80DocTypecodes_ElbowBilateralXRayTomograph,
  ElbowBilateralCT: C80DocTypecodes_ElbowBilateralCT,
  ElbowLeftCT: C80DocTypecodes_ElbowLeftCT,
  ElbowLeftXRayTomograph: C80DocTypecodes_ElbowLeftXRayTomograph,
  ElbowRightCT: C80DocTypecodes_ElbowRightCT,
  EsophagusCT: C80DocTypecodes_EsophagusCT,
  ExtremityXRayTomograph: C80DocTypecodes_ExtremityXRayTomograph,
  LowerExtremityCT: C80DocTypecodes_LowerExtremityCT,
  LowerExtremityXRayTomograph: C80DocTypecodes_LowerExtremityXRayTomograph,
  LowerExtremityBilateralCT: C80DocTypecodes_LowerExtremityBilateralCT,
  LowerExtremityVesselsBilateralMRIAngiogram: C80DocTypecodes_LowerExtremityVesselsBilateralMRIAngiogram,
  LowerExtremityBilateralMRI: C80DocTypecodes_LowerExtremityBilateralMRI,
  LowerExtremityLeftCT: C80DocTypecodes_LowerExtremityLeftCT,
  LowerExtremityLeftXRayTomograph: C80DocTypecodes_LowerExtremityLeftXRayTomograph,
  LowerExtremityLeftMRI: C80DocTypecodes_LowerExtremityLeftMRI,
  LowerExtremityRightCT: C80DocTypecodes_LowerExtremityRightCT,
  LowerExtremityRightMRI: C80DocTypecodes_LowerExtremityRightMRI,
  UpperExtremityCT: C80DocTypecodes_UpperExtremityCT,
  UpperExtremityLeftCT: C80DocTypecodes_UpperExtremityLeftCT,
  UpperExtremityRightCT: C80DocTypecodes_UpperExtremityRightCT,
  FemurCT: C80DocTypecodes_FemurCT,
  FemurXRayTomograph: C80DocTypecodes_FemurXRayTomograph,
  FemurBilateralXRayTomograph: C80DocTypecodes_FemurBilateralXRayTomograph,
  FemurLeftCT: C80DocTypecodes_FemurLeftCT,
  FemurLeftXRayTomograph: C80DocTypecodes_FemurLeftXRayTomograph,
  FemurRightCT: C80DocTypecodes_FemurRightCT,
  FetalMRI: C80DocTypecodes_FetalMRI,
  FootCT: C80DocTypecodes_FootCT,
  FootXRayTomograph: C80DocTypecodes_FootXRayTomograph,
  FootBilateralCT: C80DocTypecodes_FootBilateralCT,
  FootLeftCT: C80DocTypecodes_FootLeftCT,
  FootLeftXRayTomograph: C80DocTypecodes_FootLeftXRayTomograph,
  FootRightCT: C80DocTypecodes_FootRightCT,
  ForearmCT: C80DocTypecodes_ForearmCT,
  ForearmBilateralCT: C80DocTypecodes_ForearmBilateralCT,
  ForearmLeftCT: C80DocTypecodes_ForearmLeftCT,
  ForearmRightCT: C80DocTypecodes_ForearmRightCT,
  GallbladderXRayTomograph: C80DocTypecodes_GallbladderXRayTomograph,
  HandCT: C80DocTypecodes_HandCT,
  HandXRayTomograph: C80DocTypecodes_HandXRayTomograph,
  HandBilateralCT: C80DocTypecodes_HandBilateralCT,
  HandLeftCT: C80DocTypecodes_HandLeftCT,
  HandLeftXRayTomograph: C80DocTypecodes_HandLeftXRayTomograph,
  HandRightCT: C80DocTypecodes_HandRightCT,
  WristAndHandMRI: C80DocTypecodes_WristAndHandMRI,
  HeartMRIAngiogram: C80DocTypecodes_HeartMRIAngiogram,
  CalcaneusCT: C80DocTypecodes_CalcaneusCT,
  CalcaneusXRayTomograph: C80DocTypecodes_CalcaneusXRayTomograph,
  HipXRayTomograph: C80DocTypecodes_HipXRayTomograph,
  HipMRI: C80DocTypecodes_HipMRI,
  HipCT: C80DocTypecodes_HipCT,
  HipBilateralXRayTomograph: C80DocTypecodes_HipBilateralXRayTomograph,
  HipBilateralCT: C80DocTypecodes_HipBilateralCT,
  HipBilateralMRI: C80DocTypecodes_HipBilateralMRI,
  HipLeftCT: C80DocTypecodes_HipLeftCT,
  HipLeftXRayTomograph: C80DocTypecodes_HipLeftXRayTomograph,
  HipLeftMRI: C80DocTypecodes_HipLeftMRI,
  HipRightCT: C80DocTypecodes_HipRightCT,
  HipRightMRI: C80DocTypecodes_HipRightMRI,
  UpperArmCT: C80DocTypecodes_UpperArmCT,
  HumerusXRayTomograph: C80DocTypecodes_HumerusXRayTomograph,
  UpperArmMRI: C80DocTypecodes_UpperArmMRI,
  UpperArmBilateralCT: C80DocTypecodes_UpperArmBilateralCT,
  UpperArmLeftCT: C80DocTypecodes_UpperArmLeftCT,
  UpperArmLeftMRI: C80DocTypecodes_UpperArmLeftMRI,
  UpperArmRightCT: C80DocTypecodes_UpperArmRightCT,
  UpperArmRightMRI: C80DocTypecodes_UpperArmRightMRI,
  SacroiliacJointMRI: C80DocTypecodes_SacroiliacJointMRI,
  KidneyXRayTomograph: C80DocTypecodes_KidneyXRayTomograph,
  KidneyMRI: C80DocTypecodes_KidneyMRI,
  KidneyBilateralMRI: C80DocTypecodes_KidneyBilateralMRI,
  KidneyLeftMRI: C80DocTypecodes_KidneyLeftMRI,
  KidneyRightMRI: C80DocTypecodes_KidneyRightMRI,
  KneeCT: C80DocTypecodes_KneeCT,
  KneeXRayTomograph: C80DocTypecodes_KneeXRayTomograph,
  KneeBilateralXRayTomograph: C80DocTypecodes_KneeBilateralXRayTomograph,
  KneeBilateralCT: C80DocTypecodes_KneeBilateralCT,
  KneeLeftCT: C80DocTypecodes_KneeLeftCT,
  KneeLeftXRayTomograph: C80DocTypecodes_KneeLeftXRayTomograph,
  KneeRightCT: C80DocTypecodes_KneeRightCT,
  LarynxXRayTomograph: C80DocTypecodes_LarynxXRayTomograph,
  LarynxMRI: C80DocTypecodes_LarynxMRI,
  LiverMRI: C80DocTypecodes_LiverMRI,
  MandibleCT: C80DocTypecodes_MandibleCT,
  MandibleXRayTomograph: C80DocTypecodes_MandibleXRayTomograph,
  MaxillaAndMandibleCT: C80DocTypecodes_MaxillaAndMandibleCT,
  MaxillaCT: C80DocTypecodes_MaxillaCT,
  NeckCT: C80DocTypecodes_NeckCT,
  PancreasMRI: C80DocTypecodes_PancreasMRI,
  ParathyroidMRI: C80DocTypecodes_ParathyroidMRI,
  ThoracicOutletCT: C80DocTypecodes_ThoracicOutletCT,
  PosteriorFossaCT: C80DocTypecodes_PosteriorFossaCT,
  PosteriorFossaMRI: C80DocTypecodes_PosteriorFossaMRI,
  ProstateCT: C80DocTypecodes_ProstateCT,
  SacrumCT: C80DocTypecodes_SacrumCT,
  SacrumMRI: C80DocTypecodes_SacrumMRI,
  SacrumAndCoccyxMRI: C80DocTypecodes_SacrumAndCoccyxMRI,
  ScapulaMRI: C80DocTypecodes_ScapulaMRI,
  ShoulderCT: C80DocTypecodes_ShoulderCT,
  ShoulderBilateralCT: C80DocTypecodes_ShoulderBilateralCT,
  ShoulderLeftCT: C80DocTypecodes_ShoulderLeftCT,
  ShoulderLeftXRayTomograph: C80DocTypecodes_ShoulderLeftXRayTomograph,
  ShoulderRightCT: C80DocTypecodes_ShoulderRightCT,
  SpineMRI: C80DocTypecodes_SpineMRI,
  SpineCervicalXRayTomograph: C80DocTypecodes_SpineCervicalXRayTomograph,
  SpineLumbarXRayTomograph: C80DocTypecodes_SpineLumbarXRayTomograph,
  SpleenMRI: C80DocTypecodes_SpleenMRI,
  SternumCT: C80DocTypecodes_SternumCT,
  SternumMRI: C80DocTypecodes_SternumMRI,
  ScrotumAndTesticleMRI: C80DocTypecodes_ScrotumAndTesticleMRI,
  LowerLegCT: C80DocTypecodes_LowerLegCT,
  LowerLegLeftMRI: C80DocTypecodes_LowerLegLeftMRI,
  LowerLegRightMRI: C80DocTypecodes_LowerLegRightMRI,
  PortalVeinMRIAngiogram: C80DocTypecodes_PortalVeinMRIAngiogram,
  RenalVeinMRIAngiogram: C80DocTypecodes_RenalVeinMRIAngiogram,
  LowerExtremityVeinsMRIAngiogram: C80DocTypecodes_LowerExtremityVeinsMRIAngiogram,
  UpperExtremityVeinsMRIAngiogram: C80DocTypecodes_UpperExtremityVeinsMRIAngiogram,
  VenaCavaMRIAngiogram: C80DocTypecodes_VenaCavaMRIAngiogram,
  InferiorVenaCavaMRIAngiogram: C80DocTypecodes_InferiorVenaCavaMRIAngiogram,
  InferiorVenaCavaMRI: C80DocTypecodes_InferiorVenaCavaMRI,
  UpperExtremityVesselsMRIAngiogram: C80DocTypecodes_UpperExtremityVesselsMRIAngiogram,
  NeckVesselsMRIAngiogram: C80DocTypecodes_NeckVesselsMRIAngiogram,
  AbdomenCTLimited: C80DocTypecodes_AbdomenCTLimited,
  HeadCTLimited: C80DocTypecodes_HeadCTLimited,
  InternalAuditoryCanalMRILimited: C80DocTypecodes_InternalAuditoryCanalMRILimited,
  ChestCTLimited: C80DocTypecodes_ChestCTLimited,
  ExtremityCTLimited: C80DocTypecodes_ExtremityCTLimited,
  HeartMRILimited: C80DocTypecodes_HeartMRILimited,
  HipCTLimited: C80DocTypecodes_HipCTLimited,
  LowerExtremityJointMRILimited: C80DocTypecodes_LowerExtremityJointMRILimited,
  UpperExtremityJointMRILimited: C80DocTypecodes_UpperExtremityJointMRILimited,
  AbdomenCTLimitedWContrastIV: C80DocTypecodes_AbdomenCTLimitedWContrastIV,
  BrainMRILimitedWContrastIV: C80DocTypecodes_BrainMRILimitedWContrastIV,
  UpperExtremityCTLimitedWContrastIV: C80DocTypecodes_UpperExtremityCTLimitedWContrastIV,
  PelvisCTLimitedWContrastIV: C80DocTypecodes_PelvisCTLimitedWContrastIV,
  SpineCervicalCTLimitedWContrastIV: C80DocTypecodes_SpineCervicalCTLimitedWContrastIV,
  SpineLumbarMRILimitedWContrastIV: C80DocTypecodes_SpineLumbarMRILimitedWContrastIV,
  SpineThoracicMRILimitedWContrastIV: C80DocTypecodes_SpineThoracicMRILimitedWContrastIV,
  AbdomenCTLimitedWAndWOContrastIV: C80DocTypecodes_AbdomenCTLimitedWAndWOContrastIV,
  AbdomenCTLimitedWOContrast: C80DocTypecodes_AbdomenCTLimitedWOContrast,
  HeadCTLimitedWOContrast: C80DocTypecodes_HeadCTLimitedWOContrast,
  BrainMRILimitedWOContrast: C80DocTypecodes_BrainMRILimitedWOContrast,
  LowerExtremityCTLimitedWOContrast: C80DocTypecodes_LowerExtremityCTLimitedWOContrast,
  LowerExtremityJointLeftMRILimitedWOContrast: C80DocTypecodes_LowerExtremityJointLeftMRILimitedWOContrast,
  PelvisCTLimitedWOContrast: C80DocTypecodes_PelvisCTLimitedWOContrast,
  SpineCervicalCTLimitedWOContrast: C80DocTypecodes_SpineCervicalCTLimitedWOContrast,
  SpineLumbarCTLimitedWOContrast: C80DocTypecodes_SpineLumbarCTLimitedWOContrast,
  SpineLumbarMRILimitedWOContrast: C80DocTypecodes_SpineLumbarMRILimitedWOContrast,
  SpineThoracicMRILimitedWOContrast: C80DocTypecodes_SpineThoracicMRILimitedWOContrast,
  KidneyMRIWContrastIV: C80DocTypecodes_KidneyMRIWContrastIV,
  BreastBilateralMRIDynamicWContrastIV: C80DocTypecodes_BreastBilateralMRIDynamicWContrastIV,
  AnkleMRIWContrastIntraarticular: C80DocTypecodes_AnkleMRIWContrastIntraarticular,
  AnkleLeftMRIWContrastIntraarticular: C80DocTypecodes_AnkleLeftMRIWContrastIntraarticular,
  AnkleRightMRIWContrastIntraarticular: C80DocTypecodes_AnkleRightMRIWContrastIntraarticular,
  ElbowLeftMRIWContrastIntraarticular: C80DocTypecodes_ElbowLeftMRIWContrastIntraarticular,
  ElbowRightMRIWContrastIntraarticular: C80DocTypecodes_ElbowRightMRIWContrastIntraarticular,
  HipMRIWContrastIntraarticular: C80DocTypecodes_HipMRIWContrastIntraarticular,
  HipLeftMRIWContrastIntraarticular: C80DocTypecodes_HipLeftMRIWContrastIntraarticular,
  HipRightMRIWContrastIntraarticular: C80DocTypecodes_HipRightMRIWContrastIntraarticular,
  SacroiliacJointCTWContrastIntraarticular: C80DocTypecodes_SacroiliacJointCTWContrastIntraarticular,
  KneeCTWContrastIntraarticular: C80DocTypecodes_KneeCTWContrastIntraarticular,
  KneeMRIWContrastIntraarticular: C80DocTypecodes_KneeMRIWContrastIntraarticular,
  KneeLeftMRIWContrastIntraarticular: C80DocTypecodes_KneeLeftMRIWContrastIntraarticular,
  KneeRightMRIWContrastIntraarticular: C80DocTypecodes_KneeRightMRIWContrastIntraarticular,
  ShoulderCTWContrastIntraarticular: C80DocTypecodes_ShoulderCTWContrastIntraarticular,
  ShoulderMRIWContrastIntraarticular: C80DocTypecodes_ShoulderMRIWContrastIntraarticular,
  ShoulderLeftMRIWContrastIntraarticular: C80DocTypecodes_ShoulderLeftMRIWContrastIntraarticular,
  ShoulderRightCTWContrastIntraarticular: C80DocTypecodes_ShoulderRightCTWContrastIntraarticular,
  ShoulderRightMRIWContrastIntraarticular: C80DocTypecodes_ShoulderRightMRIWContrastIntraarticular,
  AbdomenMRIWContrastIV: C80DocTypecodes_AbdomenMRIWContrastIV,
  AnkleCTWContrastIV: C80DocTypecodes_AnkleCTWContrastIV,
  AnkleMRIWContrastIV: C80DocTypecodes_AnkleMRIWContrastIV,
  AnkleLeftCTWContrastIV: C80DocTypecodes_AnkleLeftCTWContrastIV,
  AnkleLeftMRIWContrastIV: C80DocTypecodes_AnkleLeftMRIWContrastIV,
  AnkleRightCTWContrastIV: C80DocTypecodes_AnkleRightCTWContrastIV,
  AnkleRightMRIWContrastIV: C80DocTypecodes_AnkleRightMRIWContrastIV,
  AortaCTAngiogramWContrastIV: C80DocTypecodes_AortaCTAngiogramWContrastIV,
  AortaCTWContrastIV: C80DocTypecodes_AortaCTWContrastIV,
  AortaAbdominalCTWContrastIV: C80DocTypecodes_AortaAbdominalCTWContrastIV,
  AorticArchCTAngiogramWContrastIV: C80DocTypecodes_AorticArchCTAngiogramWContrastIV,
  AppendixCTWContrastIV: C80DocTypecodes_AppendixCTWContrastIV,
  CarotidArteryCTAngiogramWContrastIV: C80DocTypecodes_CarotidArteryCTAngiogramWContrastIV,
  PulmonaryArteryCTAngiogramWContrastIV: C80DocTypecodes_PulmonaryArteryCTAngiogramWContrastIV,
  FaceMRIWContrastIV: C80DocTypecodes_FaceMRIWContrastIV,
  BreastMRIWContrastIV: C80DocTypecodes_BreastMRIWContrastIV,
  BreastBilateralMRIWContrastIV: C80DocTypecodes_BreastBilateralMRIWContrastIV,
  BreastLeftMRIWContrastIV: C80DocTypecodes_BreastLeftMRIWContrastIV,
  BreastRightMRIWContrastIV: C80DocTypecodes_BreastRightMRIWContrastIV,
  CalcaneusLeftCTWContrastIV: C80DocTypecodes_CalcaneusLeftCTWContrastIV,
  CalcaneusRightCTWContrastIV: C80DocTypecodes_CalcaneusRightCTWContrastIV,
  InternalAuditoryCanalMRIWContrastIV: C80DocTypecodes_InternalAuditoryCanalMRIWContrastIV,
  ChestMRIWContrastIV: C80DocTypecodes_ChestMRIWContrastIV,
  ElbowCTWContrastIV: C80DocTypecodes_ElbowCTWContrastIV,
  ElbowMRIWContrastIV: C80DocTypecodes_ElbowMRIWContrastIV,
  ElbowLeftCTWContrastIV: C80DocTypecodes_ElbowLeftCTWContrastIV,
  ElbowLeftMRIWContrastIV: C80DocTypecodes_ElbowLeftMRIWContrastIV,
  ElbowRightCTWContrastIV: C80DocTypecodes_ElbowRightCTWContrastIV,
  ElbowRightMRIWContrastIV: C80DocTypecodes_ElbowRightMRIWContrastIV,
  LowerExtremityBilateralMRIWContrastIV: C80DocTypecodes_LowerExtremityBilateralMRIWContrastIV,
  LowerExtremityLeftCTWContrastIV: C80DocTypecodes_LowerExtremityLeftCTWContrastIV,
  LowerExtremityLeftMRIWContrastIV: C80DocTypecodes_LowerExtremityLeftMRIWContrastIV,
  LowerExtremityRightCTWContrastIV: C80DocTypecodes_LowerExtremityRightCTWContrastIV,
  LowerExtremityRightMRIWContrastIV: C80DocTypecodes_LowerExtremityRightMRIWContrastIV,
  UpperExtremityBilateralCTWContrastIV: C80DocTypecodes_UpperExtremityBilateralCTWContrastIV,
  UpperExtremityLeftCTWContrastIV: C80DocTypecodes_UpperExtremityLeftCTWContrastIV,
  UpperExtremityRightCTWContrastIV: C80DocTypecodes_UpperExtremityRightCTWContrastIV,
  UpperExtremityRightMRIWContrastIV: C80DocTypecodes_UpperExtremityRightMRIWContrastIV,
  FemurCTWContrastIV: C80DocTypecodes_FemurCTWContrastIV,
  ThighMRIWContrastIV: C80DocTypecodes_ThighMRIWContrastIV,
  FemurLeftCTWContrastIV: C80DocTypecodes_FemurLeftCTWContrastIV,
  ThighLeftMRIWContrastIV: C80DocTypecodes_ThighLeftMRIWContrastIV,
  FemurRightCTWContrastIV: C80DocTypecodes_FemurRightCTWContrastIV,
  ThighRightMRIWContrastIV: C80DocTypecodes_ThighRightMRIWContrastIV,
  FootCTWContrastIV: C80DocTypecodes_FootCTWContrastIV,
  FootMRIWContrastIV: C80DocTypecodes_FootMRIWContrastIV,
  FootBilateralMRIWContrastIV: C80DocTypecodes_FootBilateralMRIWContrastIV,
  FootLeftCTWContrastIV: C80DocTypecodes_FootLeftCTWContrastIV,
  FootLeftMRIWContrastIV: C80DocTypecodes_FootLeftMRIWContrastIV,
  FootRightCTWContrastIV: C80DocTypecodes_FootRightCTWContrastIV,
  FootRightMRIWContrastIV: C80DocTypecodes_FootRightMRIWContrastIV,
  ForearmCTWContrastIV: C80DocTypecodes_ForearmCTWContrastIV,
  ForearmMRIWContrastIV: C80DocTypecodes_ForearmMRIWContrastIV,
  ForearmLeftCTWContrastIV: C80DocTypecodes_ForearmLeftCTWContrastIV,
  ForearmLeftMRIWContrastIV: C80DocTypecodes_ForearmLeftMRIWContrastIV,
  ForearmRightCTWContrastIV: C80DocTypecodes_ForearmRightCTWContrastIV,
  ForearmRightMRIWContrastIV: C80DocTypecodes_ForearmRightMRIWContrastIV,
  HandCTWContrastIV: C80DocTypecodes_HandCTWContrastIV,
  HandMRIWContrastIV: C80DocTypecodes_HandMRIWContrastIV,
  HandLeftCTWContrastIV: C80DocTypecodes_HandLeftCTWContrastIV,
  HandLeftMRIWContrastIV: C80DocTypecodes_HandLeftMRIWContrastIV,
  HandRightCTWContrastIV: C80DocTypecodes_HandRightCTWContrastIV,
  HandRightMRIWContrastIV: C80DocTypecodes_HandRightMRIWContrastIV,
  HeartMRIWContrastIV: C80DocTypecodes_HeartMRIWContrastIV,
  CalcaneusCTWContrastIV: C80DocTypecodes_CalcaneusCTWContrastIV,
  HipMRIWContrastIV: C80DocTypecodes_HipMRIWContrastIV,
  HipCTWContrastIV: C80DocTypecodes_HipCTWContrastIV,
  HipBilateralCTWContrastIV: C80DocTypecodes_HipBilateralCTWContrastIV,
  HipBilateralMRIWContrastIV: C80DocTypecodes_HipBilateralMRIWContrastIV,
  HipLeftCTWContrastIV: C80DocTypecodes_HipLeftCTWContrastIV,
  HipLeftMRIWContrastIV: C80DocTypecodes_HipLeftMRIWContrastIV,
  HipRightCTWContrastIV: C80DocTypecodes_HipRightCTWContrastIV,
  HipRightMRIWContrastIV: C80DocTypecodes_HipRightMRIWContrastIV,
  UpperArmCTWContrastIV: C80DocTypecodes_UpperArmCTWContrastIV,
  UpperArmMRIWContrastIV: C80DocTypecodes_UpperArmMRIWContrastIV,
  UpperArmLeftCTWContrastIV: C80DocTypecodes_UpperArmLeftCTWContrastIV,
  UpperArmLeftMRIWContrastIV: C80DocTypecodes_UpperArmLeftMRIWContrastIV,
  UpperArmRightCTWContrastIV: C80DocTypecodes_UpperArmRightCTWContrastIV,
  UpperArmRightMRIWContrastIV: C80DocTypecodes_UpperArmRightMRIWContrastIV,
  LowerExtremityJointMRIWContrastIV: C80DocTypecodes_LowerExtremityJointMRIWContrastIV,
  LowerExtremityJointLeftMRIWContrastIV: C80DocTypecodes_LowerExtremityJointLeftMRIWContrastIV,
  LowerExtremityJointRightMRIWContrastIV: C80DocTypecodes_LowerExtremityJointRightMRIWContrastIV,
  UpperExtremityJointMRIWContrastIV: C80DocTypecodes_UpperExtremityJointMRIWContrastIV,
  SacroiliacJointCTWContrastIV: C80DocTypecodes_SacroiliacJointCTWContrastIV,
  SacroiliacJointMRIWContrastIV: C80DocTypecodes_SacroiliacJointMRIWContrastIV,
  KidneyBilateralMRIWContrastIV: C80DocTypecodes_KidneyBilateralMRIWContrastIV,
  KidneyLeftMRIWContrastIV: C80DocTypecodes_KidneyLeftMRIWContrastIV,
  KidneyRightMRIWContrastIV: C80DocTypecodes_KidneyRightMRIWContrastIV,
  KneeCTWContrastIV: C80DocTypecodes_KneeCTWContrastIV,
  KneeMRIWContrastIV: C80DocTypecodes_KneeMRIWContrastIV,
  KneeBilateralMRIWContrastIV: C80DocTypecodes_KneeBilateralMRIWContrastIV,
  KneeLeftCTWContrastIV: C80DocTypecodes_KneeLeftCTWContrastIV,
  KneeLeftMRIWContrastIV: C80DocTypecodes_KneeLeftMRIWContrastIV,
  KneeRightCTWContrastIV: C80DocTypecodes_KneeRightCTWContrastIV,
  KneeRightMRIWContrastIV: C80DocTypecodes_KneeRightMRIWContrastIV,
  LarynxCTWContrastIV: C80DocTypecodes_LarynxCTWContrastIV,
  LarynxMRIWContrastIV: C80DocTypecodes_LarynxMRIWContrastIV,
  LiverMRIWContrastIV: C80DocTypecodes_LiverMRIWContrastIV,
  MandibleCTWContrastIV: C80DocTypecodes_MandibleCTWContrastIV,
  NasopharynxMRIWContrastIV: C80DocTypecodes_NasopharynxMRIWContrastIV,
  NeckVesselsCTAngiogramWContrastIV: C80DocTypecodes_NeckVesselsCTAngiogramWContrastIV,
  NeckCTWContrastIV: C80DocTypecodes_NeckCTWContrastIV,
  PancreasMRIWContrastIV: C80DocTypecodes_PancreasMRIWContrastIV,
  PelvisMRIWContrastIV: C80DocTypecodes_PelvisMRIWContrastIV,
  PituitaryAndSellaTurcicaMRIWContrastIV: C80DocTypecodes_PituitaryAndSellaTurcicaMRIWContrastIV,
  ThoracicOutletMRIWContrastIV: C80DocTypecodes_ThoracicOutletMRIWContrastIV,
  ThoracicOutletLeftMRIWContrastIV: C80DocTypecodes_ThoracicOutletLeftMRIWContrastIV,
  ThoracicOutletRightMRIWContrastIV: C80DocTypecodes_ThoracicOutletRightMRIWContrastIV,
  PosteriorFossaCTWContrastIV: C80DocTypecodes_PosteriorFossaCTWContrastIV,
  PosteriorFossaMRIWContrastIV: C80DocTypecodes_PosteriorFossaMRIWContrastIV,
  ProstateMRIWContrastIV: C80DocTypecodes_ProstateMRIWContrastIV,
  SacrumCTWContrastIV: C80DocTypecodes_SacrumCTWContrastIV,
  SacrumMRIWContrastIV: C80DocTypecodes_SacrumMRIWContrastIV,
  SacrumAndCoccyxMRIWContrastIV: C80DocTypecodes_SacrumAndCoccyxMRIWContrastIV,
  ScapulaLeftMRIWContrastIV: C80DocTypecodes_ScapulaLeftMRIWContrastIV,
  ScapulaRightMRIWContrastIV: C80DocTypecodes_ScapulaRightMRIWContrastIV,
  ShoulderCTWContrastIV: C80DocTypecodes_ShoulderCTWContrastIV,
  ShoulderMRIWContrastIV: C80DocTypecodes_ShoulderMRIWContrastIV,
  ShoulderLeftCTWContrastIV: C80DocTypecodes_ShoulderLeftCTWContrastIV,
  ShoulderRightCTWContrastIV: C80DocTypecodes_ShoulderRightCTWContrastIV,
  ShoulderRightMRIWContrastIV: C80DocTypecodes_ShoulderRightMRIWContrastIV,
  SinusesCTWContrastIV: C80DocTypecodes_SinusesCTWContrastIV,
  SpineMRIWContrastIV: C80DocTypecodes_SpineMRIWContrastIV,
  SternumCTWContrastIV: C80DocTypecodes_SternumCTWContrastIV,
  LowerLegCTWContrastIV: C80DocTypecodes_LowerLegCTWContrastIV,
  LowerLegMRIWContrastIV: C80DocTypecodes_LowerLegMRIWContrastIV,
  LowerLegLeftCTWContrastIV: C80DocTypecodes_LowerLegLeftCTWContrastIV,
  LowerLegLeftMRIWContrastIV: C80DocTypecodes_LowerLegLeftMRIWContrastIV,
  LowerLegRightCTWContrastIV: C80DocTypecodes_LowerLegRightCTWContrastIV,
  LowerLegRightMRIWContrastIV: C80DocTypecodes_LowerLegRightMRIWContrastIV,
  UterusCTWContrastIV: C80DocTypecodes_UterusCTWContrastIV,
  UterusMRIWContrastIV: C80DocTypecodes_UterusMRIWContrastIV,
  ChestVesselsCTAngiogramWContrastIV: C80DocTypecodes_ChestVesselsCTAngiogramWContrastIV,
  AbdomenCTWAndWOContrastIV: C80DocTypecodes_AbdomenCTWAndWOContrastIV,
  AnkleCTWAndWOContrastIV: C80DocTypecodes_AnkleCTWAndWOContrastIV,
  AnkleLeftCTWAndWOContrastIV: C80DocTypecodes_AnkleLeftCTWAndWOContrastIV,
  AnkleRightCTWAndWOContrastIV: C80DocTypecodes_AnkleRightCTWAndWOContrastIV,
  AortaAbdominalCTWAndWOContrastIV: C80DocTypecodes_AortaAbdominalCTWAndWOContrastIV,
  AortaAbdominalMRIAngiogramWAndWOContrastIV: C80DocTypecodes_AortaAbdominalMRIAngiogramWAndWOContrastIV,
  AortaAbdominalMRIWAndWOContrastIV: C80DocTypecodes_AortaAbdominalMRIWAndWOContrastIV,
  AortaThoracicMRIAngiogramWAndWOContrastIV: C80DocTypecodes_AortaThoracicMRIAngiogramWAndWOContrastIV,
  RenalArteryMRIAngiogramWAndWOContrastIV: C80DocTypecodes_RenalArteryMRIAngiogramWAndWOContrastIV,
  BreastMRIWAndWOContrastIV: C80DocTypecodes_BreastMRIWAndWOContrastIV,
  BreastBilateralMRIWAndWOContrastIV: C80DocTypecodes_BreastBilateralMRIWAndWOContrastIV,
  BreastLeftMRIWAndWOContrastIV: C80DocTypecodes_BreastLeftMRIWAndWOContrastIV,
  BreastRightMRIWAndWOContrastIV: C80DocTypecodes_BreastRightMRIWAndWOContrastIV,
  CalcaneusLeftCTWAndWOContrastIV: C80DocTypecodes_CalcaneusLeftCTWAndWOContrastIV,
  CalcaneusRightCTWAndWOContrastIV: C80DocTypecodes_CalcaneusRightCTWAndWOContrastIV,
  InternalAuditoryCanalCTWAndWOContrastIV: C80DocTypecodes_InternalAuditoryCanalCTWAndWOContrastIV,
  ChestMRIWAndWOContrastIV: C80DocTypecodes_ChestMRIWAndWOContrastIV,
  ChestAndAbdomenMRIWAndWOContrastIV: C80DocTypecodes_ChestAndAbdomenMRIWAndWOContrastIV,
  ElbowCTWAndWOContrastIV: C80DocTypecodes_ElbowCTWAndWOContrastIV,
  ElbowLeftCTWAndWOContrastIV: C80DocTypecodes_ElbowLeftCTWAndWOContrastIV,
  ElbowRightCTWAndWOContrastIV: C80DocTypecodes_ElbowRightCTWAndWOContrastIV,
  LowerExtremityCTWAndWOContrastIV: C80DocTypecodes_LowerExtremityCTWAndWOContrastIV,
  LowerExtremityBilateralMRIWAndWOContrastIV: C80DocTypecodes_LowerExtremityBilateralMRIWAndWOContrastIV,
  LowerExtremityLeftCTWAndWOContrastIV: C80DocTypecodes_LowerExtremityLeftCTWAndWOContrastIV,
  LowerExtremityLeftMRIWAndWOContrastIV: C80DocTypecodes_LowerExtremityLeftMRIWAndWOContrastIV,
  LowerExtremityRightCTWAndWOContrastIV: C80DocTypecodes_LowerExtremityRightCTWAndWOContrastIV,
  AbdomenXRay3Views: C80DocTypecodes_AbdomenXRay3Views,
  AnkleXRay3Views: C80DocTypecodes_AnkleXRay3Views,
  AnkleBilateralXRay3Views: C80DocTypecodes_AnkleBilateralXRay3Views,
  AnkleLeftXRay3Views: C80DocTypecodes_AnkleLeftXRay3Views,
  FacialBonesXRay3Views: C80DocTypecodes_FacialBonesXRay3Views,
  ChestXRay3Views: C80DocTypecodes_ChestXRay3Views,
  ElbowXRay3Views: C80DocTypecodes_ElbowXRay3Views,
  ElbowBilateralXRay3Views: C80DocTypecodes_ElbowBilateralXRay3Views,
  ElbowLeftXRay3Views: C80DocTypecodes_ElbowLeftXRay3Views,
  FemurXRay3Views: C80DocTypecodes_FemurXRay3Views,
  FingerXRay3Views: C80DocTypecodes_FingerXRay3Views,
  FingerLeftXRay3Views: C80DocTypecodes_FingerLeftXRay3Views,
  FootXRay3Views: C80DocTypecodes_FootXRay3Views,
  FootBilateralXRay3Views: C80DocTypecodes_FootBilateralXRay3Views,
  FootLeftXRay3Views: C80DocTypecodes_FootLeftXRay3Views,
  HipBilateralXRay3Views: C80DocTypecodes_HipBilateralXRay3Views,
  HipLeftXRay3Views: C80DocTypecodes_HipLeftXRay3Views,
  KneeBilateralXRay3Views: C80DocTypecodes_KneeBilateralXRay3Views,
  KneeLeftXRay3Views: C80DocTypecodes_KneeLeftXRay3Views,
  MandibleXRay3Views: C80DocTypecodes_MandibleXRay3Views,
  RibsBilateralXRay3Views: C80DocTypecodes_RibsBilateralXRay3Views,
  RibsLeftXRay3Views: C80DocTypecodes_RibsLeftXRay3Views,
  ThumbLeftXRay3Views: C80DocTypecodes_ThumbLeftXRay3Views,
  ToesLeftXRay3Views: C80DocTypecodes_ToesLeftXRay3Views,
  AnkleXRay4Views: C80DocTypecodes_AnkleXRay4Views,
  FacialBonesXRay4Views: C80DocTypecodes_FacialBonesXRay4Views,
  BreastMammogram4Views: C80DocTypecodes_BreastMammogram4Views,
  ChestXRay4Views: C80DocTypecodes_ChestXRay4Views,
  ChestFluoroscopy4Views: C80DocTypecodes_ChestFluoroscopy4Views,
  ElbowBilateralXRay4Views: C80DocTypecodes_ElbowBilateralXRay4Views,
  ElbowLeftXRay4Views: C80DocTypecodes_ElbowLeftXRay4Views,
  FemurLeftXRay4Views: C80DocTypecodes_FemurLeftXRay4Views,
  KneeBilateralXRay4Views: C80DocTypecodes_KneeBilateralXRay4Views,
  KneeLeftXRay4Views: C80DocTypecodes_KneeLeftXRay4Views,
  MandibleXRay4Views: C80DocTypecodes_MandibleXRay4Views,
  RibsBilateralXRay4Views: C80DocTypecodes_RibsBilateralXRay4Views,
  ShoulderBilateralXRay4Views: C80DocTypecodes_ShoulderBilateralXRay4Views,
  ShoulderLeftXRay4Views: C80DocTypecodes_ShoulderLeftXRay4Views,
  SpineCervicalXRay4Views: C80DocTypecodes_SpineCervicalXRay4Views,
  SpineLumbarXRay4Views: C80DocTypecodes_SpineLumbarXRay4Views,
  LowerExtremityRightMRIWAndWOContrastIV: C80DocTypecodes_LowerExtremityRightMRIWAndWOContrastIV,
  UpperExtremityCTWAndWOContrastIV: C80DocTypecodes_UpperExtremityCTWAndWOContrastIV,
  UpperExtremityLeftCTWAndWOContrastIV: C80DocTypecodes_UpperExtremityLeftCTWAndWOContrastIV,
  UpperExtremityRightCTWAndWOContrastIV: C80DocTypecodes_UpperExtremityRightCTWAndWOContrastIV,
  UpperExtremityRightMRIWAndWOContrastIV: C80DocTypecodes_UpperExtremityRightMRIWAndWOContrastIV,
  FemurCTWAndWOContrastIV: C80DocTypecodes_FemurCTWAndWOContrastIV,
  FemurLeftCTWAndWOContrastIV: C80DocTypecodes_FemurLeftCTWAndWOContrastIV,
  FemurRightCTWAndWOContrastIV: C80DocTypecodes_FemurRightCTWAndWOContrastIV,
  FootCTWAndWOContrastIV: C80DocTypecodes_FootCTWAndWOContrastIV,
  FootBilateralMRIWAndWOContrastIV: C80DocTypecodes_FootBilateralMRIWAndWOContrastIV,
  FootLeftCTWAndWOContrastIV: C80DocTypecodes_FootLeftCTWAndWOContrastIV,
  FootLeftMRIWAndWOContrastIV: C80DocTypecodes_FootLeftMRIWAndWOContrastIV,
  FootRightCTWAndWOContrastIV: C80DocTypecodes_FootRightCTWAndWOContrastIV,
  FootRightMRIWAndWOContrastIV: C80DocTypecodes_FootRightMRIWAndWOContrastIV,
  ForearmCTWAndWOContrastIV: C80DocTypecodes_ForearmCTWAndWOContrastIV,
  ForearmLeftCTWAndWOContrastIV: C80DocTypecodes_ForearmLeftCTWAndWOContrastIV,
  ForearmLeftMRIWAndWOContrastIV: C80DocTypecodes_ForearmLeftMRIWAndWOContrastIV,
  ForearmRightCTWAndWOContrastIV: C80DocTypecodes_ForearmRightCTWAndWOContrastIV,
  ForearmRightMRIWAndWOContrastIV: C80DocTypecodes_ForearmRightMRIWAndWOContrastIV,
  HandCTWAndWOContrastIV: C80DocTypecodes_HandCTWAndWOContrastIV,
  HandLeftCTWAndWOContrastIV: C80DocTypecodes_HandLeftCTWAndWOContrastIV,
  HandLeftMRIWAndWOContrastIV: C80DocTypecodes_HandLeftMRIWAndWOContrastIV,
  HandRightCTWAndWOContrastIV: C80DocTypecodes_HandRightCTWAndWOContrastIV,
  HandRightMRIWAndWOContrastIV: C80DocTypecodes_HandRightMRIWAndWOContrastIV,
  HeartMRIWAndWOContrastIV: C80DocTypecodes_HeartMRIWAndWOContrastIV,
  CalcaneusCTWAndWOContrastIV: C80DocTypecodes_CalcaneusCTWAndWOContrastIV,
  HipCTWAndWOContrastIV: C80DocTypecodes_HipCTWAndWOContrastIV,
  HipBilateralCTWAndWOContrastIV: C80DocTypecodes_HipBilateralCTWAndWOContrastIV,
  HipBilateralMRIWAndWOContrastIV: C80DocTypecodes_HipBilateralMRIWAndWOContrastIV,
  HipLeftCTWAndWOContrastIV: C80DocTypecodes_HipLeftCTWAndWOContrastIV,
  HipLeftMRIWAndWOContrastIV: C80DocTypecodes_HipLeftMRIWAndWOContrastIV,
  HipRightCTWAndWOContrastIV: C80DocTypecodes_HipRightCTWAndWOContrastIV,
  HipRightMRIWAndWOContrastIV: C80DocTypecodes_HipRightMRIWAndWOContrastIV,
  UpperArmCTWAndWOContrastIV: C80DocTypecodes_UpperArmCTWAndWOContrastIV,
  UpperArmLeftCTWAndWOContrastIV: C80DocTypecodes_UpperArmLeftCTWAndWOContrastIV,
  UpperArmLeftMRIWAndWOContrastIV: C80DocTypecodes_UpperArmLeftMRIWAndWOContrastIV,
  UpperArmRightCTWAndWOContrastIV: C80DocTypecodes_UpperArmRightCTWAndWOContrastIV,
  UpperArmRightMRIWAndWOContrastIV: C80DocTypecodes_UpperArmRightMRIWAndWOContrastIV,
  LowerExtremityJointMRIWAndWOContrastIV: C80DocTypecodes_LowerExtremityJointMRIWAndWOContrastIV,
  LowerExtremityJointLeftMRIWAndWOContrastIV: C80DocTypecodes_LowerExtremityJointLeftMRIWAndWOContrastIV,
  LowerExtremityJointRightMRIWAndWOContrastIV: C80DocTypecodes_LowerExtremityJointRightMRIWAndWOContrastIV,
  UpperExtremityJointMRIWAndWOContrastIV: C80DocTypecodes_UpperExtremityJointMRIWAndWOContrastIV,
  SacroiliacJointCTWAndWOContrastIV: C80DocTypecodes_SacroiliacJointCTWAndWOContrastIV,
  SacroiliacJointMRIWAndWOContrastIV: C80DocTypecodes_SacroiliacJointMRIWAndWOContrastIV,
  KidneyBilateralCTWAndWOContrastIV: C80DocTypecodes_KidneyBilateralCTWAndWOContrastIV,
  KidneyBilateralMRIWAndWOContrastIV: C80DocTypecodes_KidneyBilateralMRIWAndWOContrastIV,
  KneeCTWAndWOContrastIV: C80DocTypecodes_KneeCTWAndWOContrastIV,
  KneeLeftCTWAndWOContrastIV: C80DocTypecodes_KneeLeftCTWAndWOContrastIV,
  KneeRightCTWAndWOContrastIV: C80DocTypecodes_KneeRightCTWAndWOContrastIV,
  LarynxMRIWAndWOContrastIV: C80DocTypecodes_LarynxMRIWAndWOContrastIV,
  MandibleCTWAndWOContrastIV: C80DocTypecodes_MandibleCTWAndWOContrastIV,
  NasopharynxMRIWAndWOContrastIV: C80DocTypecodes_NasopharynxMRIWAndWOContrastIV,
  PancreasMRIWAndWOContrastIV: C80DocTypecodes_PancreasMRIWAndWOContrastIV,
  PosteriorFossaCTWAndWOContrastIV: C80DocTypecodes_PosteriorFossaCTWAndWOContrastIV,
  PosteriorFossaMRIWAndWOContrastIV: C80DocTypecodes_PosteriorFossaMRIWAndWOContrastIV,
  ProstateMRIWAndWOContrastIV: C80DocTypecodes_ProstateMRIWAndWOContrastIV,
  SacrumCTWAndWOContrastIV: C80DocTypecodes_SacrumCTWAndWOContrastIV,
  SacrumMRIWAndWOContrastIV: C80DocTypecodes_SacrumMRIWAndWOContrastIV,
  SacrumAndCoccyxMRIWAndWOContrastIV: C80DocTypecodes_SacrumAndCoccyxMRIWAndWOContrastIV,
  ScapulaLeftMRIWAndWOContrastIV: C80DocTypecodes_ScapulaLeftMRIWAndWOContrastIV,
  ScapulaRightMRIWAndWOContrastIV: C80DocTypecodes_ScapulaRightMRIWAndWOContrastIV,
  ShoulderCTWAndWOContrastIV: C80DocTypecodes_ShoulderCTWAndWOContrastIV,
  ShoulderLeftCTWAndWOContrastIV: C80DocTypecodes_ShoulderLeftCTWAndWOContrastIV,
  ShoulderRightCTWAndWOContrastIV: C80DocTypecodes_ShoulderRightCTWAndWOContrastIV,
  SinusesCTWAndWOContrastIV: C80DocTypecodes_SinusesCTWAndWOContrastIV,
  SpineCTWAndWOContrastIV: C80DocTypecodes_SpineCTWAndWOContrastIV,
  SpineMRIWAndWOContrastIV: C80DocTypecodes_SpineMRIWAndWOContrastIV,
  SpineCervicalCTWAndWOContrastIV: C80DocTypecodes_SpineCervicalCTWAndWOContrastIV,
  SpineLumbarCTWAndWOContrastIV: C80DocTypecodes_SpineLumbarCTWAndWOContrastIV,
  SpineThoracicCTWAndWOContrastIV: C80DocTypecodes_SpineThoracicCTWAndWOContrastIV,
  SpleenMRIWAndWOContrastIV: C80DocTypecodes_SpleenMRIWAndWOContrastIV,
  SternumCTWAndWOContrastIV: C80DocTypecodes_SternumCTWAndWOContrastIV,
  ScrotumAndTesticleMRIWAndWOContrastIV: C80DocTypecodes_ScrotumAndTesticleMRIWAndWOContrastIV,
  ThyroidMRIWAndWOContrastIV: C80DocTypecodes_ThyroidMRIWAndWOContrastIV,
  LowerLegCTWAndWOContrastIV: C80DocTypecodes_LowerLegCTWAndWOContrastIV,
  LowerLegLeftCTWAndWOContrastIV: C80DocTypecodes_LowerLegLeftCTWAndWOContrastIV,
  LowerLegLeftMRIWAndWOContrastIV: C80DocTypecodes_LowerLegLeftMRIWAndWOContrastIV,
  LowerLegRightCTWAndWOContrastIV: C80DocTypecodes_LowerLegRightCTWAndWOContrastIV,
  LowerLegRightMRIWAndWOContrastIV: C80DocTypecodes_LowerLegRightMRIWAndWOContrastIV,
  UterusMRIWAndWOContrastIV: C80DocTypecodes_UterusMRIWAndWOContrastIV,
  PortalVeinMRIAngiogramWAndWOContrastIV: C80DocTypecodes_PortalVeinMRIAngiogramWAndWOContrastIV,
  RenalVeinMRIAngiogramWAndWOContrastIV: C80DocTypecodes_RenalVeinMRIAngiogramWAndWOContrastIV,
  LowerExtremityVeinsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_LowerExtremityVeinsMRIAngiogramWAndWOContrastIV,
  UpperExtremityVeinsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_UpperExtremityVeinsMRIAngiogramWAndWOContrastIV,
  InferiorVenaCavaMRIWAndWOContrastIV: C80DocTypecodes_InferiorVenaCavaMRIWAndWOContrastIV,
  SuperiorVenaCavaMRIWAndWOContrastIV: C80DocTypecodes_SuperiorVenaCavaMRIWAndWOContrastIV,
  ChestVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_ChestVesselsMRIAngiogramWAndWOContrastIV,
  UpperExtremityVesselsCTAngiogramWAndWOContrastIV: C80DocTypecodes_UpperExtremityVesselsCTAngiogramWAndWOContrastIV,
  UpperExtremityVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_UpperExtremityVesselsMRIAngiogramWAndWOContrastIV,
  NeckVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_NeckVesselsMRIAngiogramWAndWOContrastIV,
  AbdomenCTWOContrast: C80DocTypecodes_AbdomenCTWOContrast,
  AnkleCTWOContrast: C80DocTypecodes_AnkleCTWOContrast,
  AnkleLeftCTWOContrast: C80DocTypecodes_AnkleLeftCTWOContrast,
  AnkleLeftMRIWOContrast: C80DocTypecodes_AnkleLeftMRIWOContrast,
  AnkleRightCTWOContrast: C80DocTypecodes_AnkleRightCTWOContrast,
  AnkleRightMRIWOContrast: C80DocTypecodes_AnkleRightMRIWOContrast,
  AortaCTWOContrast: C80DocTypecodes_AortaCTWOContrast,
  AortaAbdominalCTWOContrast: C80DocTypecodes_AortaAbdominalCTWOContrast,
  AortaAbdominalMRIAngiogramWOContrast: C80DocTypecodes_AortaAbdominalMRIAngiogramWOContrast,
  AortaThoracicMRIAngiogramWOContrast: C80DocTypecodes_AortaThoracicMRIAngiogramWOContrast,
  AppendixCTWOContrast: C80DocTypecodes_AppendixCTWOContrast,
  FaceMRIWOContrast: C80DocTypecodes_FaceMRIWOContrast,
  BreastMRIWOContrast: C80DocTypecodes_BreastMRIWOContrast,
  BreastBilateralMRIWOContrast: C80DocTypecodes_BreastBilateralMRIWOContrast,
  BreastLeftMRIWOContrast: C80DocTypecodes_BreastLeftMRIWOContrast,
  BreastRightMRIWOContrast: C80DocTypecodes_BreastRightMRIWOContrast,
  CalcaneusLeftCTWOContrast: C80DocTypecodes_CalcaneusLeftCTWOContrast,
  CalcaneusRightCTWOContrast: C80DocTypecodes_CalcaneusRightCTWOContrast,
  ChestMRIWOContrast: C80DocTypecodes_ChestMRIWOContrast,
  ElbowCTWOContrast: C80DocTypecodes_ElbowCTWOContrast,
  ElbowBilateralCTWOContrast: C80DocTypecodes_ElbowBilateralCTWOContrast,
  ElbowLeftCTWOContrast: C80DocTypecodes_ElbowLeftCTWOContrast,
  ElbowLeftMRIWOContrast: C80DocTypecodes_ElbowLeftMRIWOContrast,
  ElbowRightCTWOContrast: C80DocTypecodes_ElbowRightCTWOContrast,
  ElbowRightMRIWOContrast: C80DocTypecodes_ElbowRightMRIWOContrast,
  LowerExtremityBilateralCTWOContrast: C80DocTypecodes_LowerExtremityBilateralCTWOContrast,
  LowerExtremityVesselsBilateralMRIAngiogramWOContrast: C80DocTypecodes_LowerExtremityVesselsBilateralMRIAngiogramWOContrast,
  LowerExtremityBilateralMRIWOContrast: C80DocTypecodes_LowerExtremityBilateralMRIWOContrast,
  LowerExtremityLeftCTWOContrast: C80DocTypecodes_LowerExtremityLeftCTWOContrast,
  LowerExtremityLeftMRIWOContrast: C80DocTypecodes_LowerExtremityLeftMRIWOContrast,
  LowerExtremityRightCTWOContrast: C80DocTypecodes_LowerExtremityRightCTWOContrast,
  LowerExtremityRightMRIWOContrast: C80DocTypecodes_LowerExtremityRightMRIWOContrast,
  UpperExtremityBilateralCTWOContrast: C80DocTypecodes_UpperExtremityBilateralCTWOContrast,
  UpperExtremityLeftCTWOContrast: C80DocTypecodes_UpperExtremityLeftCTWOContrast,
  UpperExtremityRightCTWOContrast: C80DocTypecodes_UpperExtremityRightCTWOContrast,
  UpperExtremityRightMRIWOContrast: C80DocTypecodes_UpperExtremityRightMRIWOContrast,
  FemurCTWOContrast: C80DocTypecodes_FemurCTWOContrast,
  ThighMRIWOContrast: C80DocTypecodes_ThighMRIWOContrast,
  FemurLeftCTWOContrast: C80DocTypecodes_FemurLeftCTWOContrast,
  ThighLeftMRIWOContrast: C80DocTypecodes_ThighLeftMRIWOContrast,
  FemurRightCTWOContrast: C80DocTypecodes_FemurRightCTWOContrast,
  ThighRightMRIWOContrast: C80DocTypecodes_ThighRightMRIWOContrast,
  FootCTWOContrast: C80DocTypecodes_FootCTWOContrast,
  FootBilateralMRIWOContrast: C80DocTypecodes_FootBilateralMRIWOContrast,
  FootLeftCTWOContrast: C80DocTypecodes_FootLeftCTWOContrast,
  FootLeftMRIWOContrast: C80DocTypecodes_FootLeftMRIWOContrast,
  FootRightCTWOContrast: C80DocTypecodes_FootRightCTWOContrast,
  FootRightMRIWOContrast: C80DocTypecodes_FootRightMRIWOContrast,
  ForearmCTWOContrast: C80DocTypecodes_ForearmCTWOContrast,
  ForearmLeftCTWOContrast: C80DocTypecodes_ForearmLeftCTWOContrast,
  ForearmLeftMRIWOContrast: C80DocTypecodes_ForearmLeftMRIWOContrast,
  ForearmRightCTWOContrast: C80DocTypecodes_ForearmRightCTWOContrast,
  ForearmRightMRIWOContrast: C80DocTypecodes_ForearmRightMRIWOContrast,
  HandCTWOContrast: C80DocTypecodes_HandCTWOContrast,
  HandLeftCTWOContrast: C80DocTypecodes_HandLeftCTWOContrast,
  HandLeftMRIWOContrast: C80DocTypecodes_HandLeftMRIWOContrast,
  HandRightCTWOContrast: C80DocTypecodes_HandRightCTWOContrast,
  HandRightMRIWOContrast: C80DocTypecodes_HandRightMRIWOContrast,
  HeartMRIWOContrast: C80DocTypecodes_HeartMRIWOContrast,
  CalcaneusCTWOContrast: C80DocTypecodes_CalcaneusCTWOContrast,
  HipCTWOContrast: C80DocTypecodes_HipCTWOContrast,
  HipBilateralCTWOContrast: C80DocTypecodes_HipBilateralCTWOContrast,
  HipBilateralMRIWOContrast: C80DocTypecodes_HipBilateralMRIWOContrast,
  HipLeftCTWOContrast: C80DocTypecodes_HipLeftCTWOContrast,
  HipLeftMRIWOContrast: C80DocTypecodes_HipLeftMRIWOContrast,
  HipRightCTWOContrast: C80DocTypecodes_HipRightCTWOContrast,
  HipRightMRIWOContrast: C80DocTypecodes_HipRightMRIWOContrast,
  UpperArmCTWOContrast: C80DocTypecodes_UpperArmCTWOContrast,
  UpperArmLeftCTWOContrast: C80DocTypecodes_UpperArmLeftCTWOContrast,
  UpperArmLeftMRIWOContrast: C80DocTypecodes_UpperArmLeftMRIWOContrast,
  UpperArmRightCTWOContrast: C80DocTypecodes_UpperArmRightCTWOContrast,
  UpperArmRightMRIWOContrast: C80DocTypecodes_UpperArmRightMRIWOContrast,
  AcromioclavicularJointMRIWOContrast: C80DocTypecodes_AcromioclavicularJointMRIWOContrast,
  LowerExtremityJointMRIWOContrast: C80DocTypecodes_LowerExtremityJointMRIWOContrast,
  LowerExtremityJointLeftMRIWOContrast: C80DocTypecodes_LowerExtremityJointLeftMRIWOContrast,
  LowerExtremityJointRightMRIWOContrast: C80DocTypecodes_LowerExtremityJointRightMRIWOContrast,
  UpperExtremityJointMRIWOContrast: C80DocTypecodes_UpperExtremityJointMRIWOContrast,
  SacroiliacJointCTWOContrast: C80DocTypecodes_SacroiliacJointCTWOContrast,
  SacroiliacJointMRIWOContrast: C80DocTypecodes_SacroiliacJointMRIWOContrast,
  KidneyBilateralCTWOContrast: C80DocTypecodes_KidneyBilateralCTWOContrast,
  KidneyBilateralMRIWOContrast: C80DocTypecodes_KidneyBilateralMRIWOContrast,
  KneeCTWOContrast: C80DocTypecodes_KneeCTWOContrast,
  KneeBilateralMRIWOContrast: C80DocTypecodes_KneeBilateralMRIWOContrast,
  KneeLeftCTWOContrast: C80DocTypecodes_KneeLeftCTWOContrast,
  KneeLeftMRIWOContrast: C80DocTypecodes_KneeLeftMRIWOContrast,
  KneeRightCTWOContrast: C80DocTypecodes_KneeRightCTWOContrast,
  KneeRightMRIWOContrast: C80DocTypecodes_KneeRightMRIWOContrast,
  LarynxCTWOContrast: C80DocTypecodes_LarynxCTWOContrast,
  MandibleCTWOContrast: C80DocTypecodes_MandibleCTWOContrast,
  NasopharynxMRIWOContrast: C80DocTypecodes_NasopharynxMRIWOContrast,
  NeckCTWOContrast: C80DocTypecodes_NeckCTWOContrast,
  PancreasMRIWOContrast: C80DocTypecodes_PancreasMRIWOContrast,
  ThoracicOutletRightMRIWOContrast: C80DocTypecodes_ThoracicOutletRightMRIWOContrast,
  PosteriorFossaCTWOContrast: C80DocTypecodes_PosteriorFossaCTWOContrast,
  PosteriorFossaMRIWOContrast: C80DocTypecodes_PosteriorFossaMRIWOContrast,
  ProstateMRIWOContrast: C80DocTypecodes_ProstateMRIWOContrast,
  SacrumCTWOContrast: C80DocTypecodes_SacrumCTWOContrast,
  SacrumMRIWOContrast: C80DocTypecodes_SacrumMRIWOContrast,
  SacrumAndCoccyxMRIWOContrast: C80DocTypecodes_SacrumAndCoccyxMRIWOContrast,
  ScapulaLeftMRIWOContrast: C80DocTypecodes_ScapulaLeftMRIWOContrast,
  ShoulderCTWOContrast: C80DocTypecodes_ShoulderCTWOContrast,
  ShoulderBilateralMRIWOContrast: C80DocTypecodes_ShoulderBilateralMRIWOContrast,
  ShoulderLeftCTWOContrast: C80DocTypecodes_ShoulderLeftCTWOContrast,
  ShoulderRightCTWOContrast: C80DocTypecodes_ShoulderRightCTWOContrast,
  ShoulderRightMRIWOContrast: C80DocTypecodes_ShoulderRightMRIWOContrast,
  SinusesCTWOContrast: C80DocTypecodes_SinusesCTWOContrast,
  SpineCTWOContrast: C80DocTypecodes_SpineCTWOContrast,
  SpineMRIWOContrast: C80DocTypecodes_SpineMRIWOContrast,
  SpineThoracicMRIWOContrast: C80DocTypecodes_SpineThoracicMRIWOContrast,
  SpleenMRIWOContrast: C80DocTypecodes_SpleenMRIWOContrast,
  SternumCTWOContrast: C80DocTypecodes_SternumCTWOContrast,
  ScrotumAndTesticleMRIWOContrast: C80DocTypecodes_ScrotumAndTesticleMRIWOContrast,
  ThyroidMRIWOContrast: C80DocTypecodes_ThyroidMRIWOContrast,
  LowerLegCTWOContrast: C80DocTypecodes_LowerLegCTWOContrast,
  LowerLegLeftCTWOContrast: C80DocTypecodes_LowerLegLeftCTWOContrast,
  LowerLegLeftMRIWOContrast: C80DocTypecodes_LowerLegLeftMRIWOContrast,
  LowerLegRightCTWOContrast: C80DocTypecodes_LowerLegRightCTWOContrast,
  LowerLegRightMRIWOContrast: C80DocTypecodes_LowerLegRightMRIWOContrast,
  UterusMRIWOContrast: C80DocTypecodes_UterusMRIWOContrast,
  PortalVeinMRIAngiogramWOContrast: C80DocTypecodes_PortalVeinMRIAngiogramWOContrast,
  RenalVeinMRIAngiogramWOContrast: C80DocTypecodes_RenalVeinMRIAngiogramWOContrast,
  InferiorVenaCavaMRIWOContrast: C80DocTypecodes_InferiorVenaCavaMRIWOContrast,
  SuperiorVenaCavaMRIWOContrast: C80DocTypecodes_SuperiorVenaCavaMRIWOContrast,
  ChestVesselsMRIAngiogramWOContrast: C80DocTypecodes_ChestVesselsMRIAngiogramWOContrast,
  UpperExtremityVesselsMRIAngiogramWOContrast: C80DocTypecodes_UpperExtremityVesselsMRIAngiogramWOContrast,
  NeckVesselsMRIAngiogramWOContrast: C80DocTypecodes_NeckVesselsMRIAngiogramWOContrast,
  AbdomenXRaySingleView: C80DocTypecodes_AbdomenXRaySingleView,
  AnkleXRaySingleView: C80DocTypecodes_AnkleXRaySingleView,
  ChestXRaySingleView: C80DocTypecodes_ChestXRaySingleView,
  ClavicleXRaySingleView: C80DocTypecodes_ClavicleXRaySingleView,
  ElbowXRaySingleView: C80DocTypecodes_ElbowXRaySingleView,
  LowerExtremityBilateralXRaySingleView: C80DocTypecodes_LowerExtremityBilateralXRaySingleView,
  LowerExtremityLeftXRaySingleView: C80DocTypecodes_LowerExtremityLeftXRaySingleView,
  FemurXRaySingleView: C80DocTypecodes_FemurXRaySingleView,
  FemurLeftXRaySingleView: C80DocTypecodes_FemurLeftXRaySingleView,
  FootXRaySingleView: C80DocTypecodes_FootXRaySingleView,
  HandXRaySingleView: C80DocTypecodes_HandXRaySingleView,
  CalcaneusXRaySingleView: C80DocTypecodes_CalcaneusXRaySingleView,
  HumerusXRaySingleView: C80DocTypecodes_HumerusXRaySingleView,
  KneeBilateralXRaySingleView: C80DocTypecodes_KneeBilateralXRaySingleView,
  KneeLeftXRaySingleView: C80DocTypecodes_KneeLeftXRaySingleView,
  ShoulderBilateralXRaySingleView: C80DocTypecodes_ShoulderBilateralXRaySingleView,
  ShoulderLeftXRaySingleView: C80DocTypecodes_ShoulderLeftXRaySingleView,
  WristLeftXRaySingleView: C80DocTypecodes_WristLeftXRaySingleView,
  AnkleXRayAPSingleView: C80DocTypecodes_AnkleXRayAPSingleView,
  ChestXRayAPSingleView: C80DocTypecodes_ChestXRayAPSingleView,
  ClavicleXRayAPSingleView: C80DocTypecodes_ClavicleXRayAPSingleView,
  LowerExtremityXRayAPSingleView: C80DocTypecodes_LowerExtremityXRayAPSingleView,
  FemurXRayAPSingleView: C80DocTypecodes_FemurXRayAPSingleView,
  FingerFifthXRayAPSingleView: C80DocTypecodes_FingerFifthXRayAPSingleView,
  FingerFourthXRayAPSingleView: C80DocTypecodes_FingerFourthXRayAPSingleView,
  FingerThirdXRayAPSingleView: C80DocTypecodes_FingerThirdXRayAPSingleView,
  FootXRayAPSingleView: C80DocTypecodes_FootXRayAPSingleView,
  FootBilateralXRayAPSingleView: C80DocTypecodes_FootBilateralXRayAPSingleView,
  HipXRayAPSingleView: C80DocTypecodes_HipXRayAPSingleView,
  HipLeftXRayAPSingleView: C80DocTypecodes_HipLeftXRayAPSingleView,
  AcromioclavicularJointLeftXRayAPSingleView: C80DocTypecodes_AcromioclavicularJointLeftXRayAPSingleView,
  KneeXRayAPSingleView: C80DocTypecodes_KneeXRayAPSingleView,
  KneeBilateralXRayAPSingleView: C80DocTypecodes_KneeBilateralXRayAPSingleView,
  ShoulderBilateralXRayAPSingleView: C80DocTypecodes_ShoulderBilateralXRayAPSingleView,
  ShoulderLeftXRayAPSingleView: C80DocTypecodes_ShoulderLeftXRayAPSingleView,
  AbdomenXRayAPPortableSingleView: C80DocTypecodes_AbdomenXRayAPPortableSingleView,
  ChestXRayAPPortableSingleView: C80DocTypecodes_ChestXRayAPPortableSingleView,
  KneeBilateralXRayAPAndLateral: C80DocTypecodes_KneeBilateralXRayAPAndLateral,
  AbdomenXRayLateral: C80DocTypecodes_AbdomenXRayLateral,
  AnkleXRayLateral: C80DocTypecodes_AnkleXRayLateral,
  FemurXRayLateral: C80DocTypecodes_FemurXRayLateral,
  FingerFifthXRayLateral: C80DocTypecodes_FingerFifthXRayLateral,
  FingerFourthXRayLateral: C80DocTypecodes_FingerFourthXRayLateral,
  FingerSecondXRayLateral: C80DocTypecodes_FingerSecondXRayLateral,
  FingerThirdXRayLateral: C80DocTypecodes_FingerThirdXRayLateral,
  FootLeftXRayLateral: C80DocTypecodes_FootLeftXRayLateral,
  HandXRayLateral: C80DocTypecodes_HandXRayLateral,
  HandBilateralXRayLateral: C80DocTypecodes_HandBilateralXRayLateral,
  HandLeftXRayLateral: C80DocTypecodes_HandLeftXRayLateral,
  HipXRayLateral: C80DocTypecodes_HipXRayLateral,
  HipLeftXRayLateral: C80DocTypecodes_HipLeftXRayLateral,
  KneeXRayLateral: C80DocTypecodes_KneeXRayLateral,
  KneeBilateralXRayLateral: C80DocTypecodes_KneeBilateralXRayLateral,
  KneeLeftXRayLateral: C80DocTypecodes_KneeLeftXRayLateral,
  AbdomenXRayObliqueSingleView: C80DocTypecodes_AbdomenXRayObliqueSingleView,
  ElbowXRayOblique: C80DocTypecodes_ElbowXRayOblique,
  FemurXRayObliqueSingleView: C80DocTypecodes_FemurXRayObliqueSingleView,
  FingerFifthXRayObliqueSingleView: C80DocTypecodes_FingerFifthXRayObliqueSingleView,
  FingerFourthXRayObliqueSingleView: C80DocTypecodes_FingerFourthXRayObliqueSingleView,
  FingerSecondXRayObliqueSingleView: C80DocTypecodes_FingerSecondXRayObliqueSingleView,
  FingerThirdXRayObliqueSingleView: C80DocTypecodes_FingerThirdXRayObliqueSingleView,
  FootXRayObliqueSingleView: C80DocTypecodes_FootXRayObliqueSingleView,
  FootLeftXRayObliqueSingleView: C80DocTypecodes_FootLeftXRayObliqueSingleView,
  HandXRayObliqueSingleView: C80DocTypecodes_HandXRayObliqueSingleView,
  HipXRayObliqueSingleView: C80DocTypecodes_HipXRayObliqueSingleView,
  HipBilateralXRayObliqueSingleView: C80DocTypecodes_HipBilateralXRayObliqueSingleView,
  KneeXRayOblique: C80DocTypecodes_KneeXRayOblique,
  ChestXRayLeftAnteriorOblique: C80DocTypecodes_ChestXRayLeftAnteriorOblique,
  HandXRayPA: C80DocTypecodes_HandXRayPA,
  HandBilateralXRayPA: C80DocTypecodes_HandBilateralXRayPA,
  HandLeftXRayPA: C80DocTypecodes_HandLeftXRayPA,
  WristBilateralXRayPA: C80DocTypecodes_WristBilateralXRayPA,
  BreastMammogram: C80DocTypecodes_BreastMammogram,
  BreastBilateralMammogram: C80DocTypecodes_BreastBilateralMammogram,
  BreastLeftMammogram: C80DocTypecodes_BreastLeftMammogram,
  InternalAuditoryCanalXRay: C80DocTypecodes_InternalAuditoryCanalXRay,
  HandBilateralXRay: C80DocTypecodes_HandBilateralXRay,
  HandLeftXRay: C80DocTypecodes_HandLeftXRay,
  PelvisAndHipLeftXRay: C80DocTypecodes_PelvisAndHipLeftXRay,
  HumerusLeftXRay: C80DocTypecodes_HumerusLeftXRay,
  SacroiliacJointBilateralXRay: C80DocTypecodes_SacroiliacJointBilateralXRay,
  SacroiliacJointLeftXRay: C80DocTypecodes_SacroiliacJointLeftXRay,
  KneeBilateralXRay: C80DocTypecodes_KneeBilateralXRay,
  KneeLeftXRay: C80DocTypecodes_KneeLeftXRay,
  MaxillaXRay: C80DocTypecodes_MaxillaXRay,
  PatellaBilateralXRay: C80DocTypecodes_PatellaBilateralXRay,
  PatellaLeftXRay: C80DocTypecodes_PatellaLeftXRay,
  SpineCervicalFluoroscopy: C80DocTypecodes_SpineCervicalFluoroscopy,
  AbdomenXRay2Views: C80DocTypecodes_AbdomenXRay2Views,
  BreastLeftMammogram2Views: C80DocTypecodes_BreastLeftMammogram2Views,
  ChestXRay2Views: C80DocTypecodes_ChestXRay2Views,
  ChestFluoroscopy2Views: C80DocTypecodes_ChestFluoroscopy2Views,
  ClavicleXRay2Views: C80DocTypecodes_ClavicleXRay2Views,
  ClavicleLeftXRay2Views: C80DocTypecodes_ClavicleLeftXRay2Views,
  CoccyxXRay2Views: C80DocTypecodes_CoccyxXRay2Views,
  ElbowXRay2Views: C80DocTypecodes_ElbowXRay2Views,
  ElbowBilateralXRay2Views: C80DocTypecodes_ElbowBilateralXRay2Views,
  ElbowLeftXRay2Views: C80DocTypecodes_ElbowLeftXRay2Views,
  LowerExtremityXRay2Views: C80DocTypecodes_LowerExtremityXRay2Views,
  FemurXRay2Views: C80DocTypecodes_FemurXRay2Views,
  FemurBilateralXRay2Views: C80DocTypecodes_FemurBilateralXRay2Views,
  FemurLeftXRay2Views: C80DocTypecodes_FemurLeftXRay2Views,
  FingerXRay2Views: C80DocTypecodes_FingerXRay2Views,
  FingerLeftXRay2Views: C80DocTypecodes_FingerLeftXRay2Views,
  FootBilateralXRay2Views: C80DocTypecodes_FootBilateralXRay2Views,
  RadiusAndUlnaXRay2Views: C80DocTypecodes_RadiusAndUlnaXRay2Views,
  RadiusBilateralAndUlnaBilateralXRay2Views: C80DocTypecodes_RadiusBilateralAndUlnaBilateralXRay2Views,
  RadiusLeftAndUlnaLeftXRay2Views: C80DocTypecodes_RadiusLeftAndUlnaLeftXRay2Views,
  CalcaneusXRay2Views: C80DocTypecodes_CalcaneusXRay2Views,
  CalcaneusLeftXRay2Views: C80DocTypecodes_CalcaneusLeftXRay2Views,
  HipXRay2Views: C80DocTypecodes_HipXRay2Views,
  HipLeftXRay2Views: C80DocTypecodes_HipLeftXRay2Views,
  AcromioclavicularJointLeftXRay2Views: C80DocTypecodes_AcromioclavicularJointLeftXRay2Views,
  ScapulaLeftXRay2Views: C80DocTypecodes_ScapulaLeftXRay2Views,
  ShoulderBilateralXRay2Views: C80DocTypecodes_ShoulderBilateralXRay2Views,
  ShoulderLeftXRay2Views: C80DocTypecodes_ShoulderLeftXRay2Views,
  SpineCervicalXRay2Views: C80DocTypecodes_SpineCervicalXRay2Views,
  SpineLumbarXRay2Views: C80DocTypecodes_SpineLumbarXRay2Views,
  TibiaBilateralAndFibulaBilateralXRay2Views: C80DocTypecodes_TibiaBilateralAndFibulaBilateralXRay2Views,
  TibiaLeftAndFibulaLeftXRay2Views: C80DocTypecodes_TibiaLeftAndFibulaLeftXRay2Views,
  ToesLeftXRay2Views: C80DocTypecodes_ToesLeftXRay2Views,
  SpineLumbarXRay2ViewsPortable: C80DocTypecodes_SpineLumbarXRay2ViewsPortable,
  FacialBonesXRay5Views: C80DocTypecodes_FacialBonesXRay5Views,
  KneeLeftXRay5Views: C80DocTypecodes_KneeLeftXRay5Views,
  ShoulderLeftXRay5Views: C80DocTypecodes_ShoulderLeftXRay5Views,
  KneeBilateralXRay6Views: C80DocTypecodes_KneeBilateralXRay6Views,
  ShoulderLeftXRay6Views: C80DocTypecodes_ShoulderLeftXRay6Views,
  SpineCervicalXRay7Views: C80DocTypecodes_SpineCervicalXRay7Views,
  SpineLumbarXRay7Views: C80DocTypecodes_SpineLumbarXRay7Views,
  KneeBilateralXRay8Views: C80DocTypecodes_KneeBilateralXRay8Views,
  WristLeftXRay8Views: C80DocTypecodes_WristLeftXRay8Views,
  AnkleBilateralXRayAPAndLateral: C80DocTypecodes_AnkleBilateralXRayAPAndLateral,
  AnkleLeftXRayAPAndLateral: C80DocTypecodes_AnkleLeftXRayAPAndLateral,
  CalcaneusBilateralXRayAPAndLateral: C80DocTypecodes_CalcaneusBilateralXRayAPAndLateral,
  ChestXRayAPAndLateral: C80DocTypecodes_ChestXRayAPAndLateral,
  CoccyxXRayAPAndLateral: C80DocTypecodes_CoccyxXRayAPAndLateral,
  ElbowXRayAPAndLateral: C80DocTypecodes_ElbowXRayAPAndLateral,
  ElbowBilateralXRayAPAndLateral: C80DocTypecodes_ElbowBilateralXRayAPAndLateral,
  ElbowLeftXRayAPAndLateral: C80DocTypecodes_ElbowLeftXRayAPAndLateral,
  LowerExtremityXRayAPAndLateral: C80DocTypecodes_LowerExtremityXRayAPAndLateral,
  FemurXRayAPAndLateral: C80DocTypecodes_FemurXRayAPAndLateral,
  FemurBilateralXRayAPAndLateral: C80DocTypecodes_FemurBilateralXRayAPAndLateral,
  FemurLeftXRayAPAndLateral: C80DocTypecodes_FemurLeftXRayAPAndLateral,
  FootBilateralXRayAPAndLateral: C80DocTypecodes_FootBilateralXRayAPAndLateral,
  FootLeftXRayAPAndLateral: C80DocTypecodes_FootLeftXRayAPAndLateral,
  RadiusAndUlnaXRayAPAndLateral: C80DocTypecodes_RadiusAndUlnaXRayAPAndLateral,
  RadiusBilateralAndUlnaBilateralXRayAPAndLateral: C80DocTypecodes_RadiusBilateralAndUlnaBilateralXRayAPAndLateral,
  RadiusLeftAndUlnaLeftXRayAPAndLateral: C80DocTypecodes_RadiusLeftAndUlnaLeftXRayAPAndLateral,
  CalcaneusLeftXRayAPAndLateral: C80DocTypecodes_CalcaneusLeftXRayAPAndLateral,
  HipXRayAPAndLateral: C80DocTypecodes_HipXRayAPAndLateral,
  HipBilateralXRayAPAndLateral: C80DocTypecodes_HipBilateralXRayAPAndLateral,
  HipLeftXRayAPAndLateral: C80DocTypecodes_HipLeftXRayAPAndLateral,
  PelvisAndHipXRayAPAndLateral: C80DocTypecodes_PelvisAndHipXRayAPAndLateral,
  HumerusXRayAPAndLateral: C80DocTypecodes_HumerusXRayAPAndLateral,
  HumerusBilateralXRayAPAndLateral: C80DocTypecodes_HumerusBilateralXRayAPAndLateral,
  HumerusLeftXRayAPAndLateral: C80DocTypecodes_HumerusLeftXRayAPAndLateral,
  KneeXRayAPAndLateral: C80DocTypecodes_KneeXRayAPAndLateral,
  KneeLeftXRayAPAndLateral: C80DocTypecodes_KneeLeftXRayAPAndLateral,
  MandibleXRayAPAndLateral: C80DocTypecodes_MandibleXRayAPAndLateral,
  PatellaBilateralXRayAPAndLateral: C80DocTypecodes_PatellaBilateralXRayAPAndLateral,
  PatellaLeftXRayAPAndLateral: C80DocTypecodes_PatellaLeftXRayAPAndLateral,
  ScapulaBilateralXRayAPAndLateral: C80DocTypecodes_ScapulaBilateralXRayAPAndLateral,
  ScapulaLeftXRayAPAndLateral: C80DocTypecodes_ScapulaLeftXRayAPAndLateral,
  ShoulderBilateralXRayAPAndLateral: C80DocTypecodes_ShoulderBilateralXRayAPAndLateral,
  TibiaBilateralAndFibulaBilateralXRayAPAndLateral: C80DocTypecodes_TibiaBilateralAndFibulaBilateralXRayAPAndLateral,
  TibiaLeftAndFibulaLeftXRayAPAndLateral: C80DocTypecodes_TibiaLeftAndFibulaLeftXRayAPAndLateral,
  ToesLeftXRayAPAndLateral: C80DocTypecodes_ToesLeftXRayAPAndLateral,
  AnkleBilateralXRayAPAndLateralAndOblique: C80DocTypecodes_AnkleBilateralXRayAPAndLateralAndOblique,
  AnkleLeftXRayAPAndLateralAndOblique: C80DocTypecodes_AnkleLeftXRayAPAndLateralAndOblique,
  ElbowXRayAPAndLateralAndOblique: C80DocTypecodes_ElbowXRayAPAndLateralAndOblique,
  ElbowBilateralXRayAPAndLateralAndOblique: C80DocTypecodes_ElbowBilateralXRayAPAndLateralAndOblique,
  ElbowLeftXRayAPAndLateralAndOblique: C80DocTypecodes_ElbowLeftXRayAPAndLateralAndOblique,
  FingerXRayAPAndLateralAndOblique: C80DocTypecodes_FingerXRayAPAndLateralAndOblique,
  FingerBilateralXRayAPAndLateralAndOblique: C80DocTypecodes_FingerBilateralXRayAPAndLateralAndOblique,
  FingerLeftXRayAPAndLateralAndOblique: C80DocTypecodes_FingerLeftXRayAPAndLateralAndOblique,
  FootXRayAPAndLateralAndOblique: C80DocTypecodes_FootXRayAPAndLateralAndOblique,
  FootBilateralXRayAPAndLateralAndOblique: C80DocTypecodes_FootBilateralXRayAPAndLateralAndOblique,
  FootLeftXRayAPAndLateralAndOblique: C80DocTypecodes_FootLeftXRayAPAndLateralAndOblique,
  CalcaneusXRayAPAndLateralAndOblique: C80DocTypecodes_CalcaneusXRayAPAndLateralAndOblique,
  KneeBilateralXRayAPAndLateralAndOblique: C80DocTypecodes_KneeBilateralXRayAPAndLateralAndOblique,
  KneeLeftXRayAPAndLateralAndOblique: C80DocTypecodes_KneeLeftXRayAPAndLateralAndOblique,
  SpineCervicalXRayAPAndLateralAndOblique: C80DocTypecodes_SpineCervicalXRayAPAndLateralAndOblique,
  SpineLumbarXRayAPAndLateralAndOblique: C80DocTypecodes_SpineLumbarXRayAPAndLateralAndOblique,
  ThumbLeftXRayAPAndLateralAndOblique: C80DocTypecodes_ThumbLeftXRayAPAndLateralAndOblique,
  FacialBonesXRayLimited: C80DocTypecodes_FacialBonesXRayLimited,
  MandibleXRayLimited: C80DocTypecodes_MandibleXRayLimited,
  WristBilateralXRayLimited: C80DocTypecodes_WristBilateralXRayLimited,
  ElbowBilateralXRayOblique: C80DocTypecodes_ElbowBilateralXRayOblique,
  ElbowLeftXRayOblique: C80DocTypecodes_ElbowLeftXRayOblique,
  RadiusBilateralAndUlnaBilateralXRayOblique: C80DocTypecodes_RadiusBilateralAndUlnaBilateralXRayOblique,
  RadiusLeftAndUlnaLeftXRayOblique: C80DocTypecodes_RadiusLeftAndUlnaLeftXRayOblique,
  HumerusLeftXRayOblique: C80DocTypecodes_HumerusLeftXRayOblique,
  KneeBilateralXRayOblique: C80DocTypecodes_KneeBilateralXRayOblique,
  KneeLeftXRayOblique: C80DocTypecodes_KneeLeftXRayOblique,
  MandibleXRayOblique: C80DocTypecodes_MandibleXRayOblique,
  SpineCervicalXRayOblique: C80DocTypecodes_SpineCervicalXRayOblique,
  TibiaLeftAndFibulaLeftXRayOblique: C80DocTypecodes_TibiaLeftAndFibulaLeftXRayOblique,
  ChestXRayPAAndAPLateralDecubitus: C80DocTypecodes_ChestXRayPAAndAPLateralDecubitus,
  ChestFluoroscopyPAAndLateral: C80DocTypecodes_ChestFluoroscopyPAAndLateral,
  HandBilateralXRayPAAndLateral: C80DocTypecodes_HandBilateralXRayPAAndLateral,
  HandLeftXRayPAAndLateral: C80DocTypecodes_HandLeftXRayPAAndLateral,
  MandibleXRayPAAndLateral: C80DocTypecodes_MandibleXRayPAAndLateral,
  HandXRayPAAndLateralAndOblique: C80DocTypecodes_HandXRayPAAndLateralAndOblique,
  HandBilateralXRayPAAndLateralAndOblique: C80DocTypecodes_HandBilateralXRayPAAndLateralAndOblique,
  HandLeftXRayPAAndLateralAndOblique: C80DocTypecodes_HandLeftXRayPAAndLateralAndOblique,
  ChestXRayPAAndLateralAndObliqueAndLordotic: C80DocTypecodes_ChestXRayPAAndLateralAndObliqueAndLordotic,
  ChestXRayPAAndLordotic: C80DocTypecodes_ChestXRayPAAndLordotic,
  AVShuntFluoroscopicAngiogramAngioplastyWContrast: C80DocTypecodes_AVShuntFluoroscopicAngiogramAngioplastyWContrast,
  BiliaryDuctsFluoroscopyBalloonDilatationWContrast: C80DocTypecodes_BiliaryDuctsFluoroscopyBalloonDilatationWContrast,
  ExtremityVesselFluoroscopicAngiogramAngioplastyWContrast: C80DocTypecodes_ExtremityVesselFluoroscopicAngiogramAngioplastyWContrast,
  FemoralArteryAndPoplitealArteryFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_FemoralArteryAndPoplitealArteryFluoroscopicAngiogramAngioplastyWContrastIA,
  FemoralVesselAndPoplitealArteryFluoroscopicAngiogramAtherectomyWContrast: C80DocTypecodes_FemoralVesselAndPoplitealArteryFluoroscopicAngiogramAtherectomyWContrast,
  VesselFluoroscopicAngiogramAtherectomyWContrast: C80DocTypecodes_VesselFluoroscopicAngiogramAtherectomyWContrast,
  CoronaryArteriesFluoroscopicAngiogramAtherectomyWContrastIA: C80DocTypecodes_CoronaryArteriesFluoroscopicAngiogramAtherectomyWContrastIA,
  CTGuidanceForBiopsyOfAdrenalGland: C80DocTypecodes_CTGuidanceForBiopsyOfAdrenalGland,
  CTGuidanceForBiopsyOfMuscle: C80DocTypecodes_CTGuidanceForBiopsyOfMuscle,
  CTGuidanceForChangeOfNephrostomyTubeInKidney: C80DocTypecodes_CTGuidanceForChangeOfNephrostomyTubeInKidney,
  CTGuidanceForDrainageOfBiliaryDuctsAndGallbladder: C80DocTypecodes_CTGuidanceForDrainageOfBiliaryDuctsAndGallbladder,
  FluoroscopyGuidanceForInjectionOfJoint: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfJoint,
  CTGuidanceForPlacementOfNephrostomyTubeInKidney: C80DocTypecodes_CTGuidanceForPlacementOfNephrostomyTubeInKidney,
  TemporalBoneCT: C80DocTypecodes_TemporalBoneCT,
  UpperExtremityJointLeftMRI: C80DocTypecodes_UpperExtremityJointLeftMRI,
  UpperExtremityJointRightMRI: C80DocTypecodes_UpperExtremityJointRightMRI,
  MastoidXRayTomograph: C80DocTypecodes_MastoidXRayTomograph,
  OrbitMRI: C80DocTypecodes_OrbitMRI,
  OrbitRightMRI: C80DocTypecodes_OrbitRightMRI,
  OvaryMRI: C80DocTypecodes_OvaryMRI,
  ToeMRI: C80DocTypecodes_ToeMRI,
  AbdominalVeinsMRIAngiogram: C80DocTypecodes_AbdominalVeinsMRIAngiogram,
  SubclavianArteryMRIAngiogram: C80DocTypecodes_SubclavianArteryMRIAngiogram,
  VeinsMRIAngiogram: C80DocTypecodes_VeinsMRIAngiogram,
  LowerExtremityVeinsLeftMRIAngiogram: C80DocTypecodes_LowerExtremityVeinsLeftMRIAngiogram,
  LowerExtremityVeinsRightMRIAngiogram: C80DocTypecodes_LowerExtremityVeinsRightMRIAngiogram,
  UpperExtremityVeinsLeftMRIAngiogram: C80DocTypecodes_UpperExtremityVeinsLeftMRIAngiogram,
  UpperExtremityVeinsRightMRIAngiogram: C80DocTypecodes_UpperExtremityVeinsRightMRIAngiogram,
  NeckVeinsMRIAngiogram: C80DocTypecodes_NeckVeinsMRIAngiogram,
  PelvisVeinsMRIAngiogram: C80DocTypecodes_PelvisVeinsMRIAngiogram,
  VenaCavaInferiorAndLowerExtremityVeinsMRIAngiogram: C80DocTypecodes_VenaCavaInferiorAndLowerExtremityVeinsMRIAngiogram,
  AbdominalVesselsMRIAngiogram: C80DocTypecodes_AbdominalVesselsMRIAngiogram,
  AdrenalVesselsMRIAngiogram: C80DocTypecodes_AdrenalVesselsMRIAngiogram,
  CarotidVesselMRIAngiogram: C80DocTypecodes_CarotidVesselMRIAngiogram,
  ExtremityVesselsMRIAngiogram: C80DocTypecodes_ExtremityVesselsMRIAngiogram,
  LowerExtremityVesselsLeftMRIAngiogram: C80DocTypecodes_LowerExtremityVesselsLeftMRIAngiogram,
  LowerExtremityVesselsRightMRIAngiogram: C80DocTypecodes_LowerExtremityVesselsRightMRIAngiogram,
  UpperExtremityVesselsLeftMRIAngiogram: C80DocTypecodes_UpperExtremityVesselsLeftMRIAngiogram,
  UpperExtremityVesselsRightMRIAngiogram: C80DocTypecodes_UpperExtremityVesselsRightMRIAngiogram,
  KneeVesselsMRIAngiogram: C80DocTypecodes_KneeVesselsMRIAngiogram,
  KneeVesselsLeftMRIAngiogram: C80DocTypecodes_KneeVesselsLeftMRIAngiogram,
  KneeVesselsRightMRIAngiogram: C80DocTypecodes_KneeVesselsRightMRIAngiogram,
  OrbitVesselsMRIAngiogram: C80DocTypecodes_OrbitVesselsMRIAngiogram,
  PulmonaryVesselsMRIAngiogram: C80DocTypecodes_PulmonaryVesselsMRIAngiogram,
  RenalVesselsBilateralMRIAngiogram: C80DocTypecodes_RenalVesselsBilateralMRIAngiogram,
  ShoulderVesselsMRIAngiogram: C80DocTypecodes_ShoulderVesselsMRIAngiogram,
  ShoulderVesselsLeftMRIAngiogram: C80DocTypecodes_ShoulderVesselsLeftMRIAngiogram,
  ShoulderVesselsRightMRIAngiogram: C80DocTypecodes_ShoulderVesselsRightMRIAngiogram,
  HeadVesselsMRIAngiogramLimited: C80DocTypecodes_HeadVesselsMRIAngiogramLimited,
  HepaticArteryCTAngiogramWContrastIA: C80DocTypecodes_HepaticArteryCTAngiogramWContrastIA,
  UpperJointCTWContrastIntraarticular: C80DocTypecodes_UpperJointCTWContrastIntraarticular,
  JointCTWContrastIntraarticular: C80DocTypecodes_JointCTWContrastIntraarticular,
  JointMRIWContrastIntraarticular: C80DocTypecodes_JointMRIWContrastIntraarticular,
  AbdomenAndPelvisCTWContrastIV: C80DocTypecodes_AbdomenAndPelvisCTWContrastIV,
  HeadArteriesCTAngiogramWContrastIV: C80DocTypecodes_HeadArteriesCTAngiogramWContrastIV,
  TemporalBoneCTWContrastIV: C80DocTypecodes_TemporalBoneCTWContrastIV,
  TemporalBoneRightCTWContrastIV: C80DocTypecodes_TemporalBoneRightCTWContrastIV,
  UpperExtremityJointBilateralMRIWContrastIV: C80DocTypecodes_UpperExtremityJointBilateralMRIWContrastIV,
  UpperExtremityJointLeftMRIWContrastIV: C80DocTypecodes_UpperExtremityJointLeftMRIWContrastIV,
  UpperExtremityJointRightMRIWContrastIV: C80DocTypecodes_UpperExtremityJointRightMRIWContrastIV,
  OrbitMRIWContrastIV: C80DocTypecodes_OrbitMRIWContrastIV,
  OrbitLeftMRIWContrastIV: C80DocTypecodes_OrbitLeftMRIWContrastIV,
  OrbitRightMRIWContrastIV: C80DocTypecodes_OrbitRightMRIWContrastIV,
  OvaryMRIWContrastIV: C80DocTypecodes_OvaryMRIWContrastIV,
  LowerExtremityVeinsLeftCTWContrastIV: C80DocTypecodes_LowerExtremityVeinsLeftCTWContrastIV,
  LowerExtremityVeinsRightCTWContrastIV: C80DocTypecodes_LowerExtremityVeinsRightCTWContrastIV,
  HeadVeinsMRIAngiogramWContrastIV: C80DocTypecodes_HeadVeinsMRIAngiogramWContrastIV,
  NeckVeinsMRIAngiogramWContrastIV: C80DocTypecodes_NeckVeinsMRIAngiogramWContrastIV,
  AbdominalVesselsCTAngiogramWContrastIV: C80DocTypecodes_AbdominalVesselsCTAngiogramWContrastIV,
  CarotidVesselMRIAngiogramWContrastIV: C80DocTypecodes_CarotidVesselMRIAngiogramWContrastIV,
  HeadVesselsCTAngiogramWContrastIV: C80DocTypecodes_HeadVesselsCTAngiogramWContrastIV,
  LowerExtremityVesselsCTAngiogramWContrastIV: C80DocTypecodes_LowerExtremityVesselsCTAngiogramWContrastIV,
  OrbitVesselsMRIAngiogramWContrastIV: C80DocTypecodes_OrbitVesselsMRIAngiogramWContrastIV,
  RenalVesselsCTAngiogramWContrastIV: C80DocTypecodes_RenalVesselsCTAngiogramWContrastIV,
  VesselCTAngiogramWContrastIV: C80DocTypecodes_VesselCTAngiogramWContrastIV,
  PetrousBoneCTWAndWOContrastIV: C80DocTypecodes_PetrousBoneCTWAndWOContrastIV,
  TemporalBoneCTWAndWOContrastIV: C80DocTypecodes_TemporalBoneCTWAndWOContrastIV,
  MastoidXRay3Views: C80DocTypecodes_MastoidXRay3Views,
  MastoidXRay4Views: C80DocTypecodes_MastoidXRay4Views,
  UpperExtremityJointLeftMRIWAndWOContrastIV: C80DocTypecodes_UpperExtremityJointLeftMRIWAndWOContrastIV,
  UpperExtremityJointRightMRIWAndWOContrastIV: C80DocTypecodes_UpperExtremityJointRightMRIWAndWOContrastIV,
  OrbitMRIWAndWOContrastIV: C80DocTypecodes_OrbitMRIWAndWOContrastIV,
  OrbitLeftMRIWAndWOContrastIV: C80DocTypecodes_OrbitLeftMRIWAndWOContrastIV,
  OrbitRightMRIWAndWOContrastIV: C80DocTypecodes_OrbitRightMRIWAndWOContrastIV,
  OvaryMRIWAndWOContrastIV: C80DocTypecodes_OvaryMRIWAndWOContrastIV,
  AbdominalVeinsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_AbdominalVeinsMRIAngiogramWAndWOContrastIV,
  HeadVeinsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_HeadVeinsMRIAngiogramWAndWOContrastIV,
  ChestVeinsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_ChestVeinsMRIAngiogramWAndWOContrastIV,
  LowerExtremityVeinsLeftMRIAngiogramWAndWOContrastIV: C80DocTypecodes_LowerExtremityVeinsLeftMRIAngiogramWAndWOContrastIV,
  LowerExtremityVeinsRightMRIAngiogramWAndWOContrastIV: C80DocTypecodes_LowerExtremityVeinsRightMRIAngiogramWAndWOContrastIV,
  UpperExtremityVeinsLeftMRIAngiogramWAndWOContrastIV: C80DocTypecodes_UpperExtremityVeinsLeftMRIAngiogramWAndWOContrastIV,
  UpperExtremityVeinsRightMRIAngiogramWAndWOContrastIV: C80DocTypecodes_UpperExtremityVeinsRightMRIAngiogramWAndWOContrastIV,
  NeckVeinsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_NeckVeinsMRIAngiogramWAndWOContrastIV,
  PelvisVeinsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_PelvisVeinsMRIAngiogramWAndWOContrastIV,
  AbdominalVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_AbdominalVesselsMRIAngiogramWAndWOContrastIV,
  CarotidVesselMRIAngiogramWAndWOContrastIV: C80DocTypecodes_CarotidVesselMRIAngiogramWAndWOContrastIV,
  HeadVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_HeadVesselsMRIAngiogramWAndWOContrastIV,
  LowerExtremityVesselsLeftMRIAngiogramWAndWOContrastIV: C80DocTypecodes_LowerExtremityVesselsLeftMRIAngiogramWAndWOContrastIV,
  LowerExtremityVesselsRightMRIAngiogramWAndWOContrastIV: C80DocTypecodes_LowerExtremityVesselsRightMRIAngiogramWAndWOContrastIV,
  UpperExtremityVesselsLeftMRIAngiogramWAndWOContrastIV: C80DocTypecodes_UpperExtremityVesselsLeftMRIAngiogramWAndWOContrastIV,
  UpperExtremityVesselsRightMRIAngiogramWAndWOContrastIV: C80DocTypecodes_UpperExtremityVesselsRightMRIAngiogramWAndWOContrastIV,
  KneeVesselsRightMRIAngiogramWAndWOContrastIV: C80DocTypecodes_KneeVesselsRightMRIAngiogramWAndWOContrastIV,
  PelvisVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_PelvisVesselsMRIAngiogramWAndWOContrastIV,
  ShoulderVesselsLeftMRIAngiogramWAndWOContrastIV: C80DocTypecodes_ShoulderVesselsLeftMRIAngiogramWAndWOContrastIV,
  ShoulderVesselsRightMRIAngiogramWAndWOContrastIV: C80DocTypecodes_ShoulderVesselsRightMRIAngiogramWAndWOContrastIV,
  TemporalBoneCTWOContrast: C80DocTypecodes_TemporalBoneCTWOContrast,
  TemporalBoneLeftCTWOContrast: C80DocTypecodes_TemporalBoneLeftCTWOContrast,
  TemporalBoneRightCTWOContrast: C80DocTypecodes_TemporalBoneRightCTWOContrast,
  UpperExtremityJointLeftMRIWOContrast: C80DocTypecodes_UpperExtremityJointLeftMRIWOContrast,
  UpperExtremityJointRightMRIWOContrast: C80DocTypecodes_UpperExtremityJointRightMRIWOContrast,
  JointMRIWOContrast: C80DocTypecodes_JointMRIWOContrast,
  OrbitMRIWOContrast: C80DocTypecodes_OrbitMRIWOContrast,
  OrbitLeftMRIWOContrast: C80DocTypecodes_OrbitLeftMRIWOContrast,
  OrbitRightMRIWOContrast: C80DocTypecodes_OrbitRightMRIWOContrast,
  OvaryMRIWOContrast: C80DocTypecodes_OvaryMRIWOContrast,
  HeadVeinsMRIAngiogramWOContrast: C80DocTypecodes_HeadVeinsMRIAngiogramWOContrast,
  NeckVeinsMRIAngiogramWOContrast: C80DocTypecodes_NeckVeinsMRIAngiogramWOContrast,
  AbdominalVesselsMRIAngiogramWOContrast: C80DocTypecodes_AbdominalVesselsMRIAngiogramWOContrast,
  AnkleVesselsMRIAngiogramWOContrast: C80DocTypecodes_AnkleVesselsMRIAngiogramWOContrast,
  CarotidVesselMRIAngiogramWOContrast: C80DocTypecodes_CarotidVesselMRIAngiogramWOContrast,
  HeadVesselsMRIAngiogramWOContrast: C80DocTypecodes_HeadVesselsMRIAngiogramWOContrast,
  LowerExtremityVesselsLeftMRIAngiogramWOContrast: C80DocTypecodes_LowerExtremityVesselsLeftMRIAngiogramWOContrast,
  PelvisVesselsMRIAngiogramWOContrast: C80DocTypecodes_PelvisVesselsMRIAngiogramWOContrast,
  OrbitXRay: C80DocTypecodes_OrbitXRay,
  OrbitLeftXRay: C80DocTypecodes_OrbitLeftXRay,
  MastoidXRay5Views: C80DocTypecodes_MastoidXRay5Views,
  MastoidXRayLimited: C80DocTypecodes_MastoidXRayLimited,
  TibiaAndFibulaXRayOblique: C80DocTypecodes_TibiaAndFibulaXRayOblique,
  CTGuidanceForAspirationAndPlacementOfDrainageTubeOfAbdomen: C80DocTypecodes_CTGuidanceForAspirationAndPlacementOfDrainageTubeOfAbdomen,
  CTGuidanceForBiopsyOfFacialBonesAndMaxilla: C80DocTypecodes_CTGuidanceForBiopsyOfFacialBonesAndMaxilla,
  CTGuidanceStereotacticForBiopsyOfHead: C80DocTypecodes_CTGuidanceStereotacticForBiopsyOfHead,
  CTGuidanceStereotacticForBiopsyOfHeadWOContrast: C80DocTypecodes_CTGuidanceStereotacticForBiopsyOfHeadWOContrast,
  AdrenalGlandCT: C80DocTypecodes_AdrenalGlandCT,
  AdrenalGlandMRI: C80DocTypecodes_AdrenalGlandMRI,
  PituitaryAndSellaTurcicaCT: C80DocTypecodes_PituitaryAndSellaTurcicaCT,
  SalivaryGlandMRI: C80DocTypecodes_SalivaryGlandMRI,
  HeartCTForScoring: C80DocTypecodes_HeartCTForScoring,
  HeartCTForScoringWContrastIV: C80DocTypecodes_HeartCTForScoringWContrastIV,
  MRIGuidanceStereotacticForBiopsyOfBrain: C80DocTypecodes_MRIGuidanceStereotacticForBiopsyOfBrain,
  FacialBonesAndMaxillaCTLimited: C80DocTypecodes_FacialBonesAndMaxillaCTLimited,
  FacialBonesAndMaxillaCTLimitedWOContrast: C80DocTypecodes_FacialBonesAndMaxillaCTLimitedWOContrast,
  SpineCTStereotactic: C80DocTypecodes_SpineCTStereotactic,
  UnspecifiedBodyRegionCTStereotactic: C80DocTypecodes_UnspecifiedBodyRegionCTStereotactic,
  SalivaryGlandCTWContrastIntraSalivaryDuct: C80DocTypecodes_SalivaryGlandCTWContrastIntraSalivaryDuct,
  ChestAndAbdomenMRIWContrastIV: C80DocTypecodes_ChestAndAbdomenMRIWContrastIV,
  AdrenalGlandCTWContrastIV: C80DocTypecodes_AdrenalGlandCTWContrastIV,
  BiliaryDuctsAndPancreaticDuctMRIWAndWOContrastIV: C80DocTypecodes_BiliaryDuctsAndPancreaticDuctMRIWAndWOContrastIV,
  KneeBilateralXRay2ViewsStanding: C80DocTypecodes_KneeBilateralXRay2ViewsStanding,
  SpineLumbarXRay2ViewsStanding: C80DocTypecodes_SpineLumbarXRay2ViewsStanding,
  FootBilateralXRay3ViewsStanding: C80DocTypecodes_FootBilateralXRay3ViewsStanding,
  FootLeftXRay3ViewsStanding: C80DocTypecodes_FootLeftXRay3ViewsStanding,
  SpineLumbarXRay3ViewsStanding: C80DocTypecodes_SpineLumbarXRay3ViewsStanding,
  AdrenalGlandCTWAndWOContrastIV: C80DocTypecodes_AdrenalGlandCTWAndWOContrastIV,
  AdrenalGlandMRIWAndWOContrastIV: C80DocTypecodes_AdrenalGlandMRIWAndWOContrastIV,
  AbdomenAndPelvisCTWOContrast: C80DocTypecodes_AbdomenAndPelvisCTWOContrast,
  AdrenalGlandCTWOContrast: C80DocTypecodes_AdrenalGlandCTWOContrast,
  AdrenalGlandMRIWOContrast: C80DocTypecodes_AdrenalGlandMRIWOContrast,
  ThyroidCTWOContrast: C80DocTypecodes_ThyroidCTWOContrast,
  OrbitAndFaceMRIWOContrast: C80DocTypecodes_OrbitAndFaceMRIWOContrast,
  FacialBonesAndMaxillaCTAnd3DReconstruction: C80DocTypecodes_FacialBonesAndMaxillaCTAnd3DReconstruction,
  RibsBilateralXRayAPSingleView: C80DocTypecodes_RibsBilateralXRayAPSingleView,
  RibsLeftXRayAPSingleView: C80DocTypecodes_RibsLeftXRayAPSingleView,
  ChestXRayAPUprightPortable: C80DocTypecodes_ChestXRayAPUprightPortable,
  ShoulderLeftXRayAPAndWestPointAndOutlet: C80DocTypecodes_ShoulderLeftXRayAPAndWestPointAndOutlet,
  BreastMammogramAxillary: C80DocTypecodes_BreastMammogramAxillary,
  ShoulderBilateralXRayAxillary: C80DocTypecodes_ShoulderBilateralXRayAxillary,
  ShoulderLeftXRayAxillary: C80DocTypecodes_ShoulderLeftXRayAxillary,
  HandXRayBallCatcher: C80DocTypecodes_HandXRayBallCatcher,
  HandBilateralXRayBrewerton: C80DocTypecodes_HandBilateralXRayBrewerton,
  HandLeftXRayBrewerton: C80DocTypecodes_HandLeftXRayBrewerton,
  WristBilateralXRayWClenchedFist: C80DocTypecodes_WristBilateralXRayWClenchedFist,
  WristLeftXRayLateralWExtension: C80DocTypecodes_WristLeftXRayLateralWExtension,
  WristLeftXRayLateralWFlexion: C80DocTypecodes_WristLeftXRayLateralWFlexion,
  HipXRayFriedman: C80DocTypecodes_HipXRayFriedman,
  ShoulderLeftXRayGarth: C80DocTypecodes_ShoulderLeftXRayGarth,
  CalcaneusBilateralXRayHarris: C80DocTypecodes_CalcaneusBilateralXRayHarris,
  FootXRayHarris: C80DocTypecodes_FootXRayHarris,
  CalcaneusLeftXRayHarris: C80DocTypecodes_CalcaneusLeftXRayHarris,
  KneeXRayHolmblad: C80DocTypecodes_KneeXRayHolmblad,
  ElbowXRayJones: C80DocTypecodes_ElbowXRayJones,
  ElbowLeftXRayJones: C80DocTypecodes_ElbowLeftXRayJones,
  HipXRayJudet: C80DocTypecodes_HipXRayJudet,
  HipBilateralXRayJudet: C80DocTypecodes_HipBilateralXRayJudet,
  HipLeftXRayJudet: C80DocTypecodes_HipLeftXRayJudet,
  AbdomenXRayLateralCrosstable: C80DocTypecodes_AbdomenXRayLateralCrosstable,
  HipXRayLateralCrosstable: C80DocTypecodes_HipXRayLateralCrosstable,
  HipBilateralXRayLateralCrosstable: C80DocTypecodes_HipBilateralXRayLateralCrosstable,
  HipLeftXRayLateralCrosstable: C80DocTypecodes_HipLeftXRayLateralCrosstable,
  KneeXRayLateralCrosstable: C80DocTypecodes_KneeXRayLateralCrosstable,
  SpineCervicalXRayLateralCrosstable: C80DocTypecodes_SpineCervicalXRayLateralCrosstable,
  SpineLumbarXRayLateralCrosstable: C80DocTypecodes_SpineLumbarXRayLateralCrosstable,
  SpineCervicalXRayLateralCrosstablePortable: C80DocTypecodes_SpineCervicalXRayLateralCrosstablePortable,
  SpineLumbarXRayLateralCrosstablePortable: C80DocTypecodes_SpineLumbarXRayLateralCrosstablePortable,
  HipBilateralXRayLateralFrog: C80DocTypecodes_HipBilateralXRayLateralFrog,
  HipLeftXRayLateralFrog: C80DocTypecodes_HipLeftXRayLateralFrog,
  AbdomenXRayLeftLateral: C80DocTypecodes_AbdomenXRayLeftLateral,
  AbdomenXRayRightLateral: C80DocTypecodes_AbdomenXRayRightLateral,
  SpineCervicalXRayLateralWExtension: C80DocTypecodes_SpineCervicalXRayLateralWExtension,
  SpineCervicalXRayLateralWFlexion: C80DocTypecodes_SpineCervicalXRayLateralWFlexion,
  KneeBilateralXRayLateralHyperextension: C80DocTypecodes_KneeBilateralXRayLateralHyperextension,
  KneeLeftXRayLateralHyperextension: C80DocTypecodes_KneeLeftXRayLateralHyperextension,
  FootXRayLateralStanding: C80DocTypecodes_FootXRayLateralStanding,
  KneeLeftXRayLateralStanding: C80DocTypecodes_KneeLeftXRayLateralStanding,
  SpineLumbarXRayLateralStanding: C80DocTypecodes_SpineLumbarXRayLateralStanding,
  KneeXRayLaurin: C80DocTypecodes_KneeXRayLaurin,
  BreastLeftMammogramMagnification: C80DocTypecodes_BreastLeftMammogramMagnification,
  BreastBilateralMammogramMLO: C80DocTypecodes_BreastBilateralMammogramMLO,
  AnkleXRayMortise: C80DocTypecodes_AnkleXRayMortise,
  ChestXRayLeftOblique: C80DocTypecodes_ChestXRayLeftOblique,
  SpineLumbarXRayLeftOblique: C80DocTypecodes_SpineLumbarXRayLeftOblique,
  ChestXRayRightOblique: C80DocTypecodes_ChestXRayRightOblique,
  SpineLumbarXRayRightOblique: C80DocTypecodes_SpineLumbarXRayRightOblique,
  ShoulderBilateralXRayOutlet: C80DocTypecodes_ShoulderBilateralXRayOutlet,
  ShoulderLeftXRayOutlet: C80DocTypecodes_ShoulderLeftXRayOutlet,
  KneeLeftXRayPAStanding: C80DocTypecodes_KneeLeftXRayPAStanding,
  AbdomenXRayPAProne: C80DocTypecodes_AbdomenXRayPAProne,
  BreastBilateralMammogramRoll: C80DocTypecodes_BreastBilateralMammogramRoll,
  BreastLeftMammogramRoll: C80DocTypecodes_BreastLeftMammogramRoll,
  KneeXRayRosenbergStanding: C80DocTypecodes_KneeXRayRosenbergStanding,
  KneeLeftXRayRosenbergStanding: C80DocTypecodes_KneeLeftXRayRosenbergStanding,
  KneeBilateralXRayRosenbergStanding: C80DocTypecodes_KneeBilateralXRayRosenbergStanding,
  CalcaneusBilateralXRaySkiJump: C80DocTypecodes_CalcaneusBilateralXRaySkiJump,
  CalcaneusXRaySkiJump: C80DocTypecodes_CalcaneusXRaySkiJump,
  CalcaneusLeftXRaySkiJump: C80DocTypecodes_CalcaneusLeftXRaySkiJump,
  ShoulderBilateralXRayStrykerNotch: C80DocTypecodes_ShoulderBilateralXRayStrykerNotch,
  ShoulderLeftXRayStrykerNotch: C80DocTypecodes_ShoulderLeftXRayStrykerNotch,
  SkullXRaySubmentovertex: C80DocTypecodes_SkullXRaySubmentovertex,
  KneeBilateralXRaySunrise: C80DocTypecodes_KneeBilateralXRaySunrise,
  BreastMammogramTangential: C80DocTypecodes_BreastMammogramTangential,
  BreastBilateralMammogramTangential: C80DocTypecodes_BreastBilateralMammogramTangential,
  BreastLeftMammogramTangential: C80DocTypecodes_BreastLeftMammogramTangential,
  HumerusXRayTransthoracic: C80DocTypecodes_HumerusXRayTransthoracic,
  HumerusBilateralXRayTransthoracic: C80DocTypecodes_HumerusBilateralXRayTransthoracic,
  HumerusLeftXRayTransthoracic: C80DocTypecodes_HumerusLeftXRayTransthoracic,
  ShoulderLeftXRayTransthoracic: C80DocTypecodes_ShoulderLeftXRayTransthoracic,
  ShoulderBilateralXRayGrashey: C80DocTypecodes_ShoulderBilateralXRayGrashey,
  BreastMammogramTrueLateral: C80DocTypecodes_BreastMammogramTrueLateral,
  BreastBilateralMammogramTrueLateral: C80DocTypecodes_BreastBilateralMammogramTrueLateral,
  HipXRayTrueLateral: C80DocTypecodes_HipXRayTrueLateral,
  HipLeftXRayTrueLateral: C80DocTypecodes_HipLeftXRayTrueLateral,
  KneeBilateralXRayTunnel: C80DocTypecodes_KneeBilateralXRayTunnel,
  KneeLeftXRayTunnel: C80DocTypecodes_KneeLeftXRayTunnel,
  KneeLeftXRayTunnelStanding: C80DocTypecodes_KneeLeftXRayTunnelStanding,
  WristLeftXRayUlnarDeviation: C80DocTypecodes_WristLeftXRayUlnarDeviation,
  WristBilateralXRayUlnarVariance: C80DocTypecodes_WristBilateralXRayUlnarVariance,
  AbdomenXRayUpright: C80DocTypecodes_AbdomenXRayUpright,
  ShoulderBilateralXRayVelpeauAxillary: C80DocTypecodes_ShoulderBilateralXRayVelpeauAxillary,
  ShoulderLeftXRayVelpeauAxillary: C80DocTypecodes_ShoulderLeftXRayVelpeauAxillary,
  HipXRayVonRossen: C80DocTypecodes_HipXRayVonRossen,
  ShoulderBilateralXRayWestPoint: C80DocTypecodes_ShoulderBilateralXRayWestPoint,
  ShoulderLeftXRayWestPoint: C80DocTypecodes_ShoulderLeftXRayWestPoint,
  BreastBilateralMammogramXCCL: C80DocTypecodes_BreastBilateralMammogramXCCL,
  BreastLeftMammogramXCCL: C80DocTypecodes_BreastLeftMammogramXCCL,
  ScapulaLeftXRayY: C80DocTypecodes_ScapulaLeftXRayY,
  ScapulaBilateralXRayY: C80DocTypecodes_ScapulaBilateralXRayY,
  AcromioclavicularJointBilateralXRayZanca: C80DocTypecodes_AcromioclavicularJointBilateralXRayZanca,
  AcromioclavicularJointLeftXRayZanca: C80DocTypecodes_AcromioclavicularJointLeftXRayZanca,
  CalcaneusBilateralXRayStanding: C80DocTypecodes_CalcaneusBilateralXRayStanding,
  HipBilateralXRayStanding: C80DocTypecodes_HipBilateralXRayStanding,
  FetalXRay: C80DocTypecodes_FetalXRay,
  HumerusBilateralXRay: C80DocTypecodes_HumerusBilateralXRay,
  UnspecifiedBodyRegionFluoroscopyOfForeignBody: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyOfForeignBody,
  AcetabulumLeftXRay2Views: C80DocTypecodes_AcetabulumLeftXRay2Views,
  RibsLeftXRay2Views: C80DocTypecodes_RibsLeftXRay2Views,
  ChestXRay2ViewsWNippleMarkers: C80DocTypecodes_ChestXRay2ViewsWNippleMarkers,
  FootBilateralXRay2ViewsStanding: C80DocTypecodes_FootBilateralXRay2ViewsStanding,
  FootLeftXRay2ViewsStanding: C80DocTypecodes_FootLeftXRay2ViewsStanding,
  WristBilateralXRay4Views: C80DocTypecodes_WristBilateralXRay4Views,
  WristLeftXRay4Views: C80DocTypecodes_WristLeftXRay4Views,
  WristLeftXRay5Views: C80DocTypecodes_WristLeftXRay5Views,
  SpineLumbarXRay5ViewsStanding: C80DocTypecodes_SpineLumbarXRay5ViewsStanding,
  WristLeftXRay6Views: C80DocTypecodes_WristLeftXRay6Views,
  HipXRayAPPortable: C80DocTypecodes_HipXRayAPPortable,
  AbdomenXRayAPSupineAndLateralDecubitusPortable: C80DocTypecodes_AbdomenXRayAPSupineAndLateralDecubitusPortable,
  HipXRayAPAndLateralCrosstablePortable: C80DocTypecodes_HipXRayAPAndLateralCrosstablePortable,
  SpineLumbarXRayAPAndLateralPortable: C80DocTypecodes_SpineLumbarXRayAPAndLateralPortable,
  SpineCervicalXRayAPAndLateralAndOdontoidPortable: C80DocTypecodes_SpineCervicalXRayAPAndLateralAndOdontoidPortable,
  ShoulderBilateralXRayAPAndAxillary: C80DocTypecodes_ShoulderBilateralXRayAPAndAxillary,
  ShoulderBilateralXRayAPAndAxillaryAndOutlet: C80DocTypecodes_ShoulderBilateralXRayAPAndAxillaryAndOutlet,
  ShoulderLeftXRayAPAndAxillaryAndOutlet: C80DocTypecodes_ShoulderLeftXRayAPAndAxillaryAndOutlet,
  ShoulderLeftXRayAPAndAxillaryAndOutletAndZanca: C80DocTypecodes_ShoulderLeftXRayAPAndAxillaryAndOutletAndZanca,
  ShoulderLeftXRayAPAndAxillaryAndY: C80DocTypecodes_ShoulderLeftXRayAPAndAxillaryAndY,
  AbdomenXRayAPSupineAndLateralDecubitus: C80DocTypecodes_AbdomenXRayAPSupineAndLateralDecubitus,
  HipXRayAPAndLateralCrosstable: C80DocTypecodes_HipXRayAPAndLateralCrosstable,
  HipLeftXRayAPAndLateralCrosstable: C80DocTypecodes_HipLeftXRayAPAndLateralCrosstable,
  PelvisAndHipLeftXRayAPAndLateralCrosstable: C80DocTypecodes_PelvisAndHipLeftXRayAPAndLateralCrosstable,
  PelvisAndHipXRayAPAndLateralCrosstable: C80DocTypecodes_PelvisAndHipXRayAPAndLateralCrosstable,
  KneeXRayAPAndLateralCrosstable: C80DocTypecodes_KneeXRayAPAndLateralCrosstable,
  HipXRayAPAndLateralFrog: C80DocTypecodes_HipXRayAPAndLateralFrog,
  HipBilateralXRayAPAndLateralFrog: C80DocTypecodes_HipBilateralXRayAPAndLateralFrog,
  HipLeftXRayAPAndLateralFrog: C80DocTypecodes_HipLeftXRayAPAndLateralFrog,
  PelvisAndHipLeftXRayAPAndLateralFrog: C80DocTypecodes_PelvisAndHipLeftXRayAPAndLateralFrog,
  AnkleXRayAPAndLateralAndMortise: C80DocTypecodes_AnkleXRayAPAndLateralAndMortise,
  AnkleBilateralXRayAPAndLateralAndMortise: C80DocTypecodes_AnkleBilateralXRayAPAndLateralAndMortise,
  AnkleLeftXRayAPAndLateralAndMortise: C80DocTypecodes_AnkleLeftXRayAPAndLateralAndMortise,
  SpineCervicalXRayAPAndObliqueAndLateralWFlexionAndWExtension: C80DocTypecodes_SpineCervicalXRayAPAndObliqueAndLateralWFlexionAndWExtension,
  SpineCervicalXRayAPAndLateralAndObliqueAndOdontoid: C80DocTypecodes_SpineCervicalXRayAPAndLateralAndObliqueAndOdontoid,
  SpineCervicalXRayAPAndObliqueAndOdontoidAndLateralWFlexionAndWExtension: C80DocTypecodes_SpineCervicalXRayAPAndObliqueAndOdontoidAndLateralWFlexionAndWExtension,
  SpineLumbarXRayAPAndLateralAndObliqueAndSpot: C80DocTypecodes_SpineLumbarXRayAPAndLateralAndObliqueAndSpot,
  KneeBilateralXRayAPAndLateralAndObliqueAndSunrise: C80DocTypecodes_KneeBilateralXRayAPAndLateralAndObliqueAndSunrise,
  SpineCervicalXRayAPAndLateralAndOdontoid: C80DocTypecodes_SpineCervicalXRayAPAndLateralAndOdontoid,
  SpineCervicalXRayAPAndOdontoidAndLateralWFlexionAndWExtension: C80DocTypecodes_SpineCervicalXRayAPAndOdontoidAndLateralWFlexionAndWExtension,
  SpineLumbarXRayAPAndLateralAndSpot: C80DocTypecodes_SpineLumbarXRayAPAndLateralAndSpot,
  KneeXRayAPAndLateralAndSunrise: C80DocTypecodes_KneeXRayAPAndLateralAndSunrise,
  KneeBilateralXRayAPAndLateralAndSunrise: C80DocTypecodes_KneeBilateralXRayAPAndLateralAndSunrise,
  KneeLeftXRayAPAndLateralAndSunrise: C80DocTypecodes_KneeLeftXRayAPAndLateralAndSunrise,
  PatellaBilateralXRayAPAndLateralAndSunrise: C80DocTypecodes_PatellaBilateralXRayAPAndLateralAndSunrise,
  PatellaLeftXRayAPAndLateralAndSunrise: C80DocTypecodes_PatellaLeftXRayAPAndLateralAndSunrise,
  KneeXRayAPAndLateralAndSunriseAndTunnel: C80DocTypecodes_KneeXRayAPAndLateralAndSunriseAndTunnel,
  KneeXRayAPAndLateralAndTunnel: C80DocTypecodes_KneeXRayAPAndLateralAndTunnel,
  KneeBilateralXRayAPAndLateralAndTunnel: C80DocTypecodes_KneeBilateralXRayAPAndLateralAndTunnel,
  KneeLeftXRayAPAndLateralAndTunnel: C80DocTypecodes_KneeLeftXRayAPAndLateralAndTunnel,
  KneeXRayAPAndLateralAndObliqueAndTunnel: C80DocTypecodes_KneeXRayAPAndLateralAndObliqueAndTunnel,
  KneeBilateralXRayAPAndLateralAndSunriseAndTunnel: C80DocTypecodes_KneeBilateralXRayAPAndLateralAndSunriseAndTunnel,
  KneeLeftXRayAPAndLateralAndSunriseAndTunnel: C80DocTypecodes_KneeLeftXRayAPAndLateralAndSunriseAndTunnel,
  KneeBilateralXRayAPAndLateralAndObliqueAndSunriseAndTunnel: C80DocTypecodes_KneeBilateralXRayAPAndLateralAndObliqueAndSunriseAndTunnel,
  AbdomenXRayAPAndOblique: C80DocTypecodes_AbdomenXRayAPAndOblique,
  SpineCervicalXRayAPAndOdontoidAndLateralCrosstable: C80DocTypecodes_SpineCervicalXRayAPAndOdontoidAndLateralCrosstable,
  ClavicleLeftXRayAPAndSerendipity: C80DocTypecodes_ClavicleLeftXRayAPAndSerendipity,
  ShoulderLeftXRayAPAndStrykerNotch: C80DocTypecodes_ShoulderLeftXRayAPAndStrykerNotch,
  ShoulderLeftXRayAPAndWestPoint: C80DocTypecodes_ShoulderLeftXRayAPAndWestPoint,
  ScapulaLeftXRayAPAndY: C80DocTypecodes_ScapulaLeftXRayAPAndY,
  ShoulderLeftXRayAPAndY: C80DocTypecodes_ShoulderLeftXRayAPAndY,
  ShoulderBilateralXRayAPAndAxillaryAndY: C80DocTypecodes_ShoulderBilateralXRayAPAndAxillaryAndY,
  ShoulderBilateralXRayAxillaryAndY: C80DocTypecodes_ShoulderBilateralXRayAxillaryAndY,
  ShoulderLeftXRayAxillaryAndY: C80DocTypecodes_ShoulderLeftXRayAxillaryAndY,
  AbdomenXRayRightLateralAndLeftLateral: C80DocTypecodes_AbdomenXRayRightLateralAndLeftLateral,
  SpineLumbarXRayLateralWFlexionAndWExtension: C80DocTypecodes_SpineLumbarXRayLateralWFlexionAndWExtension,
  SpineCervicalXRayLateralWFlexionAndWExtension: C80DocTypecodes_SpineCervicalXRayLateralWFlexionAndWExtension,
  AnkleBilateralXRayLateralAndMortise: C80DocTypecodes_AnkleBilateralXRayLateralAndMortise,
  AnkleLeftXRayLateralAndMortise: C80DocTypecodes_AnkleLeftXRayLateralAndMortise,
  ShoulderLeftXRayLateralAndY: C80DocTypecodes_ShoulderLeftXRayLateralAndY,
  KidneyXRayLimitedWContrastIV: C80DocTypecodes_KidneyXRayLimitedWContrastIV,
  AbdomenXRayRightObliqueAndLeftOblique: C80DocTypecodes_AbdomenXRayRightObliqueAndLeftOblique,
  SpineCervicalXRayObliqueAndLateralWFlexionAndWExtension: C80DocTypecodes_SpineCervicalXRayObliqueAndLateralWFlexionAndWExtension,
  ShoulderLeftXRayOutletAndY: C80DocTypecodes_ShoulderLeftXRayOutletAndY,
  ChestXRayPAAndRightLateral: C80DocTypecodes_ChestXRayPAAndRightLateral,
  HandBilateralXRayPAAndLateralAndBallCatcher: C80DocTypecodes_HandBilateralXRayPAAndLateralAndBallCatcher,
  ChestXRayPAAndLateralAndAPLateralDecubitus: C80DocTypecodes_ChestXRayPAAndLateralAndAPLateralDecubitus,
  ChestXRayPAAndLateralAndAPLeftLateralDecubitus: C80DocTypecodes_ChestXRayPAAndLateralAndAPLeftLateralDecubitus,
  ChestXRayPAAndLateralAndAPRightLateralDecubitus: C80DocTypecodes_ChestXRayPAAndLateralAndAPRightLateralDecubitus,
  ChestXRayPAAndLateralAndLeftOblique: C80DocTypecodes_ChestXRayPAAndLateralAndLeftOblique,
  ChestXRayPAAndLateralAndRightOblique: C80DocTypecodes_ChestXRayPAAndLateralAndRightOblique,
  MandibleXRayPAAndLateralAndObliqueAndTowne: C80DocTypecodes_MandibleXRayPAAndLateralAndObliqueAndTowne,
  PatellaLeftXRayPAAndLateralAndSunrise: C80DocTypecodes_PatellaLeftXRayPAAndLateralAndSunrise,
  ChestXRayPAAndRightObliqueAndLeftOblique: C80DocTypecodes_ChestXRayPAAndRightObliqueAndLeftOblique,
  UnspecifiedBodyRegionFluoroscopyPortable: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyPortable,
  ShoulderBilateralXRayOutletAndY: C80DocTypecodes_ShoulderBilateralXRayOutletAndY,
  MastoidXRayStenverAndArcelin: C80DocTypecodes_MastoidXRayStenverAndArcelin,
  KneeXRayObliqueAndSunrise: C80DocTypecodes_KneeXRayObliqueAndSunrise,
  KneeXRayObliqueAndSunriseAndTunnel: C80DocTypecodes_KneeXRayObliqueAndSunriseAndTunnel,
  KneeLeftXRaySunriseAndTunnel: C80DocTypecodes_KneeLeftXRaySunriseAndTunnel,
  ShoulderLeftXRayGrasheyAndOutlet: C80DocTypecodes_ShoulderLeftXRayGrasheyAndOutlet,
  ShoulderLeftXRayGrasheyAndAxillaryAndOutlet: C80DocTypecodes_ShoulderLeftXRayGrasheyAndAxillaryAndOutlet,
  FootLeftXRayTarsal: C80DocTypecodes_FootLeftXRayTarsal,
  ShoulderLeftXRayGrasheyAndAxillary: C80DocTypecodes_ShoulderLeftXRayGrasheyAndAxillary,
  ShoulderBilateralXRayGrasheyAndAxillaryAndOutletAndZanca: C80DocTypecodes_ShoulderBilateralXRayGrasheyAndAxillaryAndOutletAndZanca,
  ShoulderLeftXRayGrasheyAndOutletAndSerendipity: C80DocTypecodes_ShoulderLeftXRayGrasheyAndOutletAndSerendipity,
  KneeBilateralXRaySunriseAndTunnel: C80DocTypecodes_KneeBilateralXRaySunriseAndTunnel,
  FacialBonesXRayLateralAndCaldwellAndWaters: C80DocTypecodes_FacialBonesXRayLateralAndCaldwellAndWaters,
  FacialBonesXRayLateralAndCaldwellAndWatersAndSubmentovertex: C80DocTypecodes_FacialBonesXRayLateralAndCaldwellAndWatersAndSubmentovertex,
  FacialBonesXRayLateralAndCaldwellAndWatersAndSubmentovertexAndTowne: C80DocTypecodes_FacialBonesXRayLateralAndCaldwellAndWatersAndSubmentovertexAndTowne,
  ShoulderLeftXRayGrasheyAndWestPoint: C80DocTypecodes_ShoulderLeftXRayGrasheyAndWestPoint,
  HipXRayPortable: C80DocTypecodes_HipXRayPortable,
  HipLeftXRayPortable: C80DocTypecodes_HipLeftXRayPortable,
  HumerusXRayPortable: C80DocTypecodes_HumerusXRayPortable,
  SpineCervicalXRayPortable: C80DocTypecodes_SpineCervicalXRayPortable,
  SpineLumbarXRayPortable: C80DocTypecodes_SpineLumbarXRayPortable,
  CerebralArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CerebralArteryFluoroscopicAngiogramWContrastIA,
  CoronaryArteriesFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CoronaryArteriesFluoroscopicAngiogramWContrastIA,
  FemoralArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_FemoralArteryFluoroscopicAngiogramWContrastIA,
  FemoralArteryAndPoplitealArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_FemoralArteryAndPoplitealArteryFluoroscopicAngiogramWContrastIA,
  IliacArteryBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_IliacArteryBilateralFluoroscopicAngiogramWContrastIA,
  IliacArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_IliacArteryLeftFluoroscopicAngiogramWContrastIA,
  InferiorMesentericArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_InferiorMesentericArteryFluoroscopicAngiogramWContrastIA,
  SuperiorMesentericArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_SuperiorMesentericArteryFluoroscopicAngiogramWContrastIA,
  PoplitealArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_PoplitealArteryLeftFluoroscopicAngiogramWContrastIA,
  PulmonaryArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_PulmonaryArteryLeftFluoroscopicAngiogramWContrastIA,
  AnkleFluoroscopyWContrastIntraarticular: C80DocTypecodes_AnkleFluoroscopyWContrastIntraarticular,
  AnkleBilateralFluoroscopyWContrastIntraarticular: C80DocTypecodes_AnkleBilateralFluoroscopyWContrastIntraarticular,
  AnkleLeftFluoroscopyWContrastIntraarticular: C80DocTypecodes_AnkleLeftFluoroscopyWContrastIntraarticular,
  ElbowFluoroscopyWContrastIntraarticular: C80DocTypecodes_ElbowFluoroscopyWContrastIntraarticular,
  ElbowBilateralFluoroscopyWContrastIntraarticular: C80DocTypecodes_ElbowBilateralFluoroscopyWContrastIntraarticular,
  ElbowLeftFluoroscopyWContrastIntraarticular: C80DocTypecodes_ElbowLeftFluoroscopyWContrastIntraarticular,
  SacroiliacJointBilateralFluoroscopyWContrastIntraarticular: C80DocTypecodes_SacroiliacJointBilateralFluoroscopyWContrastIntraarticular,
  SacroiliacJointLeftFluoroscopyWContrastIntraarticular: C80DocTypecodes_SacroiliacJointLeftFluoroscopyWContrastIntraarticular,
  JointFluoroscopyWContrastIntraarticular: C80DocTypecodes_JointFluoroscopyWContrastIntraarticular,
  SpineCervicalFluoroscopyWContrastIntradisc: C80DocTypecodes_SpineCervicalFluoroscopyWContrastIntradisc,
  SpineLumbarFluoroscopyWContrastIntradisc: C80DocTypecodes_SpineLumbarFluoroscopyWContrastIntradisc,
  CerebralVeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_CerebralVeinFluoroscopicAngiogramWContrastIV,
  LowerExtremityVeinsLeftFluoroscopicAngiogramWContrastIV: C80DocTypecodes_LowerExtremityVeinsLeftFluoroscopicAngiogramWContrastIV,
  JugularVeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_JugularVeinFluoroscopicAngiogramWContrastIV,
  EsophagusXRayWContrastPO: C80DocTypecodes_EsophagusXRayWContrastPO,
  ChestFluoroscopyWContrastPO: C80DocTypecodes_ChestFluoroscopyWContrastPO,
  ChestXRayWContrastPO: C80DocTypecodes_ChestXRayWContrastPO,
  AnkleXRayStanding: C80DocTypecodes_AnkleXRayStanding,
  AnkleBilateralXRayStanding: C80DocTypecodes_AnkleBilateralXRayStanding,
  AnkleLeftXRayStanding: C80DocTypecodes_AnkleLeftXRayStanding,
  LowerExtremityXRayStanding: C80DocTypecodes_LowerExtremityXRayStanding,
  CalcaneusXRayStanding: C80DocTypecodes_CalcaneusXRayStanding,
  CalcaneusLeftXRayStanding: C80DocTypecodes_CalcaneusLeftXRayStanding,
  HipLeftXRayStanding: C80DocTypecodes_HipLeftXRayStanding,
  SpineLumbarXRayStanding: C80DocTypecodes_SpineLumbarXRayStanding,
  ToesLeftXRayStanding: C80DocTypecodes_ToesLeftXRayStanding,
  CTGuidanceForAspirationOfCystOfAbdomen: C80DocTypecodes_CTGuidanceForAspirationOfCystOfAbdomen,
  CTGuidanceForBiopsyOfBoneMarrow: C80DocTypecodes_CTGuidanceForBiopsyOfBoneMarrow,
  CTGuidanceForBiopsyOfEpididymis: C80DocTypecodes_CTGuidanceForBiopsyOfEpididymis,
  CTGuidanceForBiopsyOfMediastinum: C80DocTypecodes_CTGuidanceForBiopsyOfMediastinum,
  CTGuidanceForBiopsyOfSuperficialTissue: C80DocTypecodes_CTGuidanceForBiopsyOfSuperficialTissue,
  BrainAndLarynxMRIWContrastIV: C80DocTypecodes_BrainAndLarynxMRIWContrastIV,
  AortaEndograftCT: C80DocTypecodes_AortaEndograftCT,
  BrainStemAndNervesCranialMRI: C80DocTypecodes_BrainStemAndNervesCranialMRI,
  BrainTemporalMRI: C80DocTypecodes_BrainTemporalMRI,
  BiliaryDuctsMRI: C80DocTypecodes_BiliaryDuctsMRI,
  BiliaryDuctsAndPancreaticDuctMRI: C80DocTypecodes_BiliaryDuctsAndPancreaticDuctMRI,
  FistulaCT: C80DocTypecodes_FistulaCT,
  AnkleAndFootMRI: C80DocTypecodes_AnkleAndFootMRI,
  ParotidGlandCT: C80DocTypecodes_ParotidGlandCT,
  ParotidGlandMRI: C80DocTypecodes_ParotidGlandMRI,
  SternoclavicularJointCT: C80DocTypecodes_SternoclavicularJointCT,
  TemporomandibularJointCT: C80DocTypecodes_TemporomandibularJointCT,
  TemporomandibularJointBilateralXRayTomograph: C80DocTypecodes_TemporomandibularJointBilateralXRayTomograph,
  TemporomandibularJointBilateralMRI: C80DocTypecodes_TemporomandibularJointBilateralMRI,
  TemporomandibularJointLeftXRayTomograph: C80DocTypecodes_TemporomandibularJointLeftXRayTomograph,
  TemporomandibularJointLeftMRI: C80DocTypecodes_TemporomandibularJointLeftMRI,
  TemporomandibularJointRightMRI: C80DocTypecodes_TemporomandibularJointRightMRI,
  SpineLumbosacralJunctionCT: C80DocTypecodes_SpineLumbosacralJunctionCT,
  MediastinumXRayTomograph: C80DocTypecodes_MediastinumXRayTomograph,
  MediastinumMRI: C80DocTypecodes_MediastinumMRI,
  CircleOfWillisMRIAngiogram: C80DocTypecodes_CircleOfWillisMRIAngiogram,
  GreatVesselMRI: C80DocTypecodes_GreatVesselMRI,
  SinusTractCTWContrastIntraSinusTract: C80DocTypecodes_SinusTractCTWContrastIntraSinusTract,
  LowerExtremityJointCTWContrastIntraarticular: C80DocTypecodes_LowerExtremityJointCTWContrastIntraarticular,
  BrainAndInternalAuditoryCanalMRIWContrastIV: C80DocTypecodes_BrainAndInternalAuditoryCanalMRIWContrastIV,
  ParotidGlandCTWContrastIV: C80DocTypecodes_ParotidGlandCTWContrastIV,
  ParotidGlandMRIWContrastIV: C80DocTypecodes_ParotidGlandMRIWContrastIV,
  SternoclavicularJointCTWContrastIV: C80DocTypecodes_SternoclavicularJointCTWContrastIV,
  TemporomandibularJointCTWContrastIV: C80DocTypecodes_TemporomandibularJointCTWContrastIV,
  TemporomandibularJointMRIWContrastIV: C80DocTypecodes_TemporomandibularJointMRIWContrastIV,
  TemporomandibularJointBilateralMRIWContrastIV: C80DocTypecodes_TemporomandibularJointBilateralMRIWContrastIV,
  TemporomandibularJointLeftCTWContrastIV: C80DocTypecodes_TemporomandibularJointLeftCTWContrastIV,
  TemporomandibularJointLeftMRIWContrastIV: C80DocTypecodes_TemporomandibularJointLeftMRIWContrastIV,
  TemporomandibularJointRightCTWContrastIV: C80DocTypecodes_TemporomandibularJointRightCTWContrastIV,
  TemporomandibularJointRightMRIWContrastIV: C80DocTypecodes_TemporomandibularJointRightMRIWContrastIV,
  SoftTissueMRIWContrastIV: C80DocTypecodes_SoftTissueMRIWContrastIV,
  CircleOfWillisMRIAngiogramWContrastIV: C80DocTypecodes_CircleOfWillisMRIAngiogramWContrastIV,
  PelvisAndSpineLumbarXRay3Views: C80DocTypecodes_PelvisAndSpineLumbarXRay3Views,
  SpineLumbarAndSacroiliacJointXRay3Views: C80DocTypecodes_SpineLumbarAndSacroiliacJointXRay3Views,
  SpineLumbarAndSacrumXRay3Views: C80DocTypecodes_SpineLumbarAndSacrumXRay3Views,
  SpineLumbarAndSacrumAndCoccyxXRay3Views: C80DocTypecodes_SpineLumbarAndSacrumAndCoccyxXRay3Views,
  SpineLumbarAndSacrumAndSacroiliacJointAndCoccyxXRay3Views: C80DocTypecodes_SpineLumbarAndSacrumAndSacroiliacJointAndCoccyxXRay3Views,
  ParotidGlandMRIWAndWOContrastIV: C80DocTypecodes_ParotidGlandMRIWAndWOContrastIV,
  SternoclavicularJointCTWAndWOContrastIV: C80DocTypecodes_SternoclavicularJointCTWAndWOContrastIV,
  TemporomandibularJointCTWAndWOContrastIV: C80DocTypecodes_TemporomandibularJointCTWAndWOContrastIV,
  TemporomandibularJointMRIWAndWOContrastIV: C80DocTypecodes_TemporomandibularJointMRIWAndWOContrastIV,
  TemporomandibularJointBilateralMRIWAndWOContrastIV: C80DocTypecodes_TemporomandibularJointBilateralMRIWAndWOContrastIV,
  TemporomandibularJointLeftMRIWAndWOContrastIV: C80DocTypecodes_TemporomandibularJointLeftMRIWAndWOContrastIV,
  TemporomandibularJointRightMRIWAndWOContrastIV: C80DocTypecodes_TemporomandibularJointRightMRIWAndWOContrastIV,
  MediastinumMRIWAndWOContrastIV: C80DocTypecodes_MediastinumMRIWAndWOContrastIV,
  SpinalVeinMRIAngiogramWAndWOContrastIV: C80DocTypecodes_SpinalVeinMRIAngiogramWAndWOContrastIV,
  BrainAndInternalAuditoryCanalMRIWOContrast: C80DocTypecodes_BrainAndInternalAuditoryCanalMRIWOContrast,
  BrainAndLarynxMRIWOContrast: C80DocTypecodes_BrainAndLarynxMRIWOContrast,
  ParotidGlandCTWOContrast: C80DocTypecodes_ParotidGlandCTWOContrast,
  ParotidGlandMRIWOContrast: C80DocTypecodes_ParotidGlandMRIWOContrast,
  SternoclavicularJointCTWOContrast: C80DocTypecodes_SternoclavicularJointCTWOContrast,
  TemporomandibularJointCTWOContrast: C80DocTypecodes_TemporomandibularJointCTWOContrast,
  TemporomandibularJointMRIWOContrast: C80DocTypecodes_TemporomandibularJointMRIWOContrast,
  TemporomandibularJointBilateralMRIWOContrast: C80DocTypecodes_TemporomandibularJointBilateralMRIWOContrast,
  TemporomandibularJointLeftMRIWOContrast: C80DocTypecodes_TemporomandibularJointLeftMRIWOContrast,
  TemporomandibularJointRightMRIWOContrast: C80DocTypecodes_TemporomandibularJointRightMRIWOContrast,
  SpineLumbosacralJunctionCTWOContrast: C80DocTypecodes_SpineLumbosacralJunctionCTWOContrast,
  SoftTissueMRIWOContrast: C80DocTypecodes_SoftTissueMRIWOContrast,
  HeadCTAnd3DReconstruction: C80DocTypecodes_HeadCTAnd3DReconstruction,
  FemurAndHipCTAndAnteversionMeasurement: C80DocTypecodes_FemurAndHipCTAndAnteversionMeasurement,
  AbdomenAndFetusXRayViewForFetalAge: C80DocTypecodes_AbdomenAndFetusXRayViewForFetalAge,
  SternoclavicularJointBilateralXRaySerendipity: C80DocTypecodes_SternoclavicularJointBilateralXRaySerendipity,
  SternoclavicularJointLeftXRaySerendipity: C80DocTypecodes_SternoclavicularJointLeftXRaySerendipity,
  SpineLumbosacralJunctionXRayTrueAP: C80DocTypecodes_SpineLumbosacralJunctionXRayTrueAP,
  WristLeftXRayScaphoid: C80DocTypecodes_WristLeftXRayScaphoid,
  FacialBonesAndZygomaticArchXRay: C80DocTypecodes_FacialBonesAndZygomaticArchXRay,
  WristBilateralXRayScaphoid: C80DocTypecodes_WristBilateralXRayScaphoid,
  HumerusBicipitalGrooveXRay: C80DocTypecodes_HumerusBicipitalGrooveXRay,
  HumerusBicipitalGrooveLeftXRay: C80DocTypecodes_HumerusBicipitalGrooveLeftXRay,
  HumerusBicipitalGrooveBilateralXRay: C80DocTypecodes_HumerusBicipitalGrooveBilateralXRay,
  SternoclavicularJointBilateralXRay: C80DocTypecodes_SternoclavicularJointBilateralXRay,
  SternoclavicularJointLeftXRay: C80DocTypecodes_SternoclavicularJointLeftXRay,
  TemporomandibularJointBilateralXRay: C80DocTypecodes_TemporomandibularJointBilateralXRay,
  OlecranonLeftXRay: C80DocTypecodes_OlecranonLeftXRay,
  SkullAndFacialBonesAndMandibleXRay: C80DocTypecodes_SkullAndFacialBonesAndMandibleXRay,
  SpineLumbarAndSacrumXRay: C80DocTypecodes_SpineLumbarAndSacrumXRay,
  SpineLumbarAndSacrumAndCoccyxXRay: C80DocTypecodes_SpineLumbarAndSacrumAndCoccyxXRay,
  SpineLumbarAndSacrumAndSacroiliacJointAndCoccyxXRay: C80DocTypecodes_SpineLumbarAndSacrumAndSacroiliacJointAndCoccyxXRay,
  ToesBilateralXRay2Views: C80DocTypecodes_ToesBilateralXRay2Views,
  TemporomandibularJointBilateralXRay5Views: C80DocTypecodes_TemporomandibularJointBilateralXRay5Views,
  PelvisAndSpineLumbarXRay5Views: C80DocTypecodes_PelvisAndSpineLumbarXRay5Views,
  SpineLumbarAndSacroiliacJointXRay5Views: C80DocTypecodes_SpineLumbarAndSacroiliacJointXRay5Views,
  SpineLumbarAndSacrumXRay5Views: C80DocTypecodes_SpineLumbarAndSacrumXRay5Views,
  SpineLumbarAndSacrumAndCoccyxXRay5Views: C80DocTypecodes_SpineLumbarAndSacrumAndCoccyxXRay5Views,
  SpineLumbarAndSacrumAndSacroiliacJointAndCoccyxXRay5Views: C80DocTypecodes_SpineLumbarAndSacrumAndSacroiliacJointAndCoccyxXRay5Views,
  SpineCervicalAndSpineThoracicXRayAPAndLateral: C80DocTypecodes_SpineCervicalAndSpineThoracicXRayAPAndLateral,
  BonesXRayBoneAge: C80DocTypecodes_BonesXRayBoneAge,
  AortaAndFemoralArteryLeftFluoroscopicAngiogramRunoffWContrastIA: C80DocTypecodes_AortaAndFemoralArteryLeftFluoroscopicAngiogramRunoffWContrastIA,
  BonesXRaySurveyForMetastasis: C80DocTypecodes_BonesXRaySurveyForMetastasis,
  AbdominalAortaAndArteriesFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AbdominalAortaAndArteriesFluoroscopicAngiogramWContrastIA,
  AorticArchAndUpperExtremityArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndUpperExtremityArteryFluoroscopicAngiogramWContrastIA,
  AorticArchAndBrachialArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndBrachialArteryFluoroscopicAngiogramWContrastIA,
  AorticArchAndCarotidArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndCarotidArteryFluoroscopicAngiogramWContrastIA,
  AorticArchAndSubclavianArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndSubclavianArteryFluoroscopicAngiogramWContrastIA,
  AorticArchAndSubclavianArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndSubclavianArteryLeftFluoroscopicAngiogramWContrastIA,
  AorticArchAndVertebralArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndVertebralArteryFluoroscopicAngiogramWContrastIA,
  AorticArchAndVertebralArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndVertebralArteryLeftFluoroscopicAngiogramWContrastIA,
  AorticArchAndVertebralArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndVertebralArteryRightFluoroscopicAngiogramWContrastIA,
  AdrenalArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AdrenalArteryLeftFluoroscopicAngiogramWContrastIA,
  BrachialArteryBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_BrachialArteryBilateralFluoroscopicAngiogramWContrastIA,
  BronchialArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_BronchialArteryFluoroscopicAngiogramWContrastIA,
  CarotidArteryExternalLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryExternalLeftFluoroscopicAngiogramWContrastIA,
  CarotidArteryAndVertebralArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryAndVertebralArteryFluoroscopicAngiogramWContrastIA,
  CarotidArteryAndVertebralArteryBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryAndVertebralArteryBilateralFluoroscopicAngiogramWContrastIA,
  CarotidArteryPlusVertebralArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryPlusVertebralArteryLeftFluoroscopicAngiogramWContrastIA,
  CeliacArteryAndSuperiorMesentericArteryAndInferiorMesentericArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CeliacArteryAndSuperiorMesentericArteryAndInferiorMesentericArteryFluoroscopicAngiogramWContrastIA,
  ExtremityArteriesLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_ExtremityArteriesLeftFluoroscopicAngiogramWContrastIA,
  UpperExtremityArteriesBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_UpperExtremityArteriesBilateralFluoroscopicAngiogramWContrastIA,
  GastricArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_GastricArteryFluoroscopicAngiogramWContrastIA,
  GastricArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_GastricArteryLeftFluoroscopicAngiogramWContrastIA,
  GastroduodenalArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_GastroduodenalArteryFluoroscopicAngiogramWContrastIA,
  MaxillaryArteryInternalFluoroscopicAngiogramWContrastIA: C80DocTypecodes_MaxillaryArteryInternalFluoroscopicAngiogramWContrastIA,
  SuperiorMesentericArteryAndInferiorMesentericArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_SuperiorMesentericArteryAndInferiorMesentericArteryFluoroscopicAngiogramWContrastIA,
  CeliacArteryAndGastricArteryLeftAndSuperiorMesentericArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CeliacArteryAndGastricArteryLeftAndSuperiorMesentericArteryFluoroscopicAngiogramWContrastIA,
  PudendalArteryInternalFluoroscopicAngiogramWContrastIA: C80DocTypecodes_PudendalArteryInternalFluoroscopicAngiogramWContrastIA,
  SubclavianArteryBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_SubclavianArteryBilateralFluoroscopicAngiogramWContrastIA,
  SubclavianArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_SubclavianArteryLeftFluoroscopicAngiogramWContrastIA,
  VertebralArteryBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_VertebralArteryBilateralFluoroscopicAngiogramWContrastIA,
  TemporomandibularJointBilateralFluoroscopyWContrastIntraarticular: C80DocTypecodes_TemporomandibularJointBilateralFluoroscopyWContrastIntraarticular,
  TemporomandibularJointLeftFluoroscopyWContrastIntraarticular: C80DocTypecodes_TemporomandibularJointLeftFluoroscopyWContrastIntraarticular,
  AzygosVeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_AzygosVeinFluoroscopicAngiogramWContrastIV,
  ExtremityVeinsBilateralFluoroscopicAngiogramWContrastIV: C80DocTypecodes_ExtremityVeinsBilateralFluoroscopicAngiogramWContrastIV,
  ExtremityVeinsLeftFluoroscopicAngiogramWContrastIV: C80DocTypecodes_ExtremityVeinsLeftFluoroscopicAngiogramWContrastIV,
  LowerExtremityVeinsBilateralFluoroscopicAngiogramWContrastIV: C80DocTypecodes_LowerExtremityVeinsBilateralFluoroscopicAngiogramWContrastIV,
  UpperExtremityVeinsBilateralFluoroscopicAngiogramWContrastIV: C80DocTypecodes_UpperExtremityVeinsBilateralFluoroscopicAngiogramWContrastIV,
  FemoralVeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_FemoralVeinFluoroscopicAngiogramWContrastIV,
  IntraosseousVeinsFluoroscopicAngiogramWContrastIV: C80DocTypecodes_IntraosseousVeinsFluoroscopicAngiogramWContrastIV,
  JugularVeinLeftFluoroscopicAngiogramWContrastIV: C80DocTypecodes_JugularVeinLeftFluoroscopicAngiogramWContrastIV,
  InferiorMesentericVeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_InferiorMesentericVeinFluoroscopicAngiogramWContrastIV,
  OrbitVeinsLeftFluoroscopicAngiogramWContrastIV: C80DocTypecodes_OrbitVeinsLeftFluoroscopicAngiogramWContrastIV,
  RenalVeinLeftFluoroscopicAngiogramWContrastIV: C80DocTypecodes_RenalVeinLeftFluoroscopicAngiogramWContrastIV,
  LowerExtremityVeinFluoroscopicAngiogramAngioplastyWContrastIV: C80DocTypecodes_LowerExtremityVeinFluoroscopicAngiogramAngioplastyWContrastIV,
  FluoroscopyGuidanceForInjectionOfSpine: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpine,
  WristCT: C80DocTypecodes_WristCT,
  WristBilateralXRayTomograph: C80DocTypecodes_WristBilateralXRayTomograph,
  WristBilateralCT: C80DocTypecodes_WristBilateralCT,
  WristLeftCT: C80DocTypecodes_WristLeftCT,
  WristLeftXRayTomograph: C80DocTypecodes_WristLeftXRayTomograph,
  WristRightCT: C80DocTypecodes_WristRightCT,
  HeartMRICineForFunction: C80DocTypecodes_HeartMRICineForFunction,
  TemporomandibularJointMRICine: C80DocTypecodes_TemporomandibularJointMRICine,
  BrainMRIDiffusionWeighted: C80DocTypecodes_BrainMRIDiffusionWeighted,
  BreastMRIDynamicWContrastIV: C80DocTypecodes_BreastMRIDynamicWContrastIV,
  PituitaryAndSellaTurcicaCTDynamicWContrastIV: C80DocTypecodes_PituitaryAndSellaTurcicaCTDynamicWContrastIV,
  ChestCTHighResolution: C80DocTypecodes_ChestCTHighResolution,
  ChestCTHighResolutionWContrastIV: C80DocTypecodes_ChestCTHighResolutionWContrastIV,
  ChestCTHighResolutionWOContrast: C80DocTypecodes_ChestCTHighResolutionWOContrast,
  BrainMRISpectroscopy: C80DocTypecodes_BrainMRISpectroscopy,
  UnspecifiedBodyRegionMRISpectroscopy: C80DocTypecodes_UnspecifiedBodyRegionMRISpectroscopy,
  WristMRIWContrastIntraarticular: C80DocTypecodes_WristMRIWContrastIntraarticular,
  WristLeftMRIWContrastIntraarticular: C80DocTypecodes_WristLeftMRIWContrastIntraarticular,
  WristRightMRIWContrastIntraarticular: C80DocTypecodes_WristRightMRIWContrastIntraarticular,
  WristCTWContrastIV: C80DocTypecodes_WristCTWContrastIV,
  WristMRIWContrastIV: C80DocTypecodes_WristMRIWContrastIV,
  WristBilateralMRIWContrastIV: C80DocTypecodes_WristBilateralMRIWContrastIV,
  WristLeftCTWContrastIV: C80DocTypecodes_WristLeftCTWContrastIV,
  WristLeftMRIWContrastIV: C80DocTypecodes_WristLeftMRIWContrastIV,
  WristRightCTWContrastIV: C80DocTypecodes_WristRightCTWContrastIV,
  WristRightMRIWContrastIV: C80DocTypecodes_WristRightMRIWContrastIV,
  WristBilateralXRay3Views: C80DocTypecodes_WristBilateralXRay3Views,
  WristLeftXRay3Views: C80DocTypecodes_WristLeftXRay3Views,
  WristCTWAndWOContrastIV: C80DocTypecodes_WristCTWAndWOContrastIV,
  WristLeftCTWAndWOContrastIV: C80DocTypecodes_WristLeftCTWAndWOContrastIV,
  WristCTWOContrast: C80DocTypecodes_WristCTWOContrast,
  WristMRIWOContrast: C80DocTypecodes_WristMRIWOContrast,
  WristBilateralCTWOContrast: C80DocTypecodes_WristBilateralCTWOContrast,
  WristBilateralMRIWOContrast: C80DocTypecodes_WristBilateralMRIWOContrast,
  WristLeftCTWOContrast: C80DocTypecodes_WristLeftCTWOContrast,
  WristLeftMRIWOContrast: C80DocTypecodes_WristLeftMRIWOContrast,
  WristRightCTWOContrast: C80DocTypecodes_WristRightCTWOContrast,
  WristRightMRIWOContrast: C80DocTypecodes_WristRightMRIWOContrast,
  AcromioclavicularJointXRay10DegreeCephalicAngle: C80DocTypecodes_AcromioclavicularJointXRay10DegreeCephalicAngle,
  ShoulderBilateralXRay30DegreeCaudalAngle: C80DocTypecodes_ShoulderBilateralXRay30DegreeCaudalAngle,
  ClavicleBilateralXRay45DegreeCephalicAngle: C80DocTypecodes_ClavicleBilateralXRay45DegreeCephalicAngle,
  ClavicleLeftXRay45DegreeCephalicAngle: C80DocTypecodes_ClavicleLeftXRay45DegreeCephalicAngle,
  HandBilateralXRayBora: C80DocTypecodes_HandBilateralXRayBora,
  HandLeftXRayBora: C80DocTypecodes_HandLeftXRayBora,
  ShoulderLeftXRayGrashey: C80DocTypecodes_ShoulderLeftXRayGrashey,
  AnkleLeftXRayLateralWManualStress: C80DocTypecodes_AnkleLeftXRayLateralWManualStress,
  AnkleLeftXRayMortiseWManualStress: C80DocTypecodes_AnkleLeftXRayMortiseWManualStress,
  KneeXRayPAW45DegreeFlexion: C80DocTypecodes_KneeXRayPAW45DegreeFlexion,
  KneeXRayPAStandingAndW45DegreeFlexion: C80DocTypecodes_KneeXRayPAStandingAndW45DegreeFlexion,
  SpineCervicalAndSpineThoracicXRay: C80DocTypecodes_SpineCervicalAndSpineThoracicXRay,
  WristBilateralXRay2Views: C80DocTypecodes_WristBilateralXRay2Views,
  WristLeftXRay2Views: C80DocTypecodes_WristLeftXRay2Views,
  KneeLeftXRayAPWManualStress: C80DocTypecodes_KneeLeftXRayAPWManualStress,
  HumerusXRayAPAndTransthoracic: C80DocTypecodes_HumerusXRayAPAndTransthoracic,
  AnkleXRayBrodenWManualStress: C80DocTypecodes_AnkleXRayBrodenWManualStress,
  LowerExtremityArteriesFluoroscopicAngiogramWContrastIA: C80DocTypecodes_LowerExtremityArteriesFluoroscopicAngiogramWContrastIA,
  UpperExtremityArteriesLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_UpperExtremityArteriesLeftFluoroscopicAngiogramWContrastIA,
  TibioperonealArteriesFluoroscopicAngiogramWContrastIA: C80DocTypecodes_TibioperonealArteriesFluoroscopicAngiogramWContrastIA,
  VertebralArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_VertebralArteryLeftFluoroscopicAngiogramWContrastIA,
  CTGuidanceForAspirationOfPleuralSpace: C80DocTypecodes_CTGuidanceForAspirationOfPleuralSpace,
  CTGuidanceForBiopsyOfChestPleura: C80DocTypecodes_CTGuidanceForBiopsyOfChestPleura,
  CTGuidanceForInjectionOfSpineDiscCervical: C80DocTypecodes_CTGuidanceForInjectionOfSpineDiscCervical,
  FluoroscopyGuidanceForInjectionOfTendon: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfTendon,
  SkullBaseCT: C80DocTypecodes_SkullBaseCT,
  SpineCervicalCTWContrastIntradisc: C80DocTypecodes_SpineCervicalCTWContrastIntradisc,
  SpineVesselsMRIAngiogram: C80DocTypecodes_SpineVesselsMRIAngiogram,
  HeadVesselsAndNeckVesselsCTAngiogramWContrastIV: C80DocTypecodes_HeadVesselsAndNeckVesselsCTAngiogramWContrastIV,
  AorticStentCTAngiogramWContrastIV: C80DocTypecodes_AorticStentCTAngiogramWContrastIV,
  SpineVesselsMRIAngiogramWContrastIV: C80DocTypecodes_SpineVesselsMRIAngiogramWContrastIV,
  CervicalSpineVesselsMRIAngiogramWContrastIV: C80DocTypecodes_CervicalSpineVesselsMRIAngiogramWContrastIV,
  LumbarSpineVesselsMRIAngiogramWContrastIV: C80DocTypecodes_LumbarSpineVesselsMRIAngiogramWContrastIV,
  ThoracicSpineVesselsMRIAngiogramWContrastIV: C80DocTypecodes_ThoracicSpineVesselsMRIAngiogramWContrastIV,
  SpineVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_SpineVesselsMRIAngiogramWAndWOContrastIV,
  CervicalSpineVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_CervicalSpineVesselsMRIAngiogramWAndWOContrastIV,
  LumbarSpineVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_LumbarSpineVesselsMRIAngiogramWAndWOContrastIV,
  ThoracicSpineVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_ThoracicSpineVesselsMRIAngiogramWAndWOContrastIV,
  SpineLumbarCTWContrastIntradisc: C80DocTypecodes_SpineLumbarCTWContrastIntradisc,
  SpineVesselsMRIAngiogramWOContrast: C80DocTypecodes_SpineVesselsMRIAngiogramWOContrast,
  CervicalSpineVesselsMRIAngiogramWOContrast: C80DocTypecodes_CervicalSpineVesselsMRIAngiogramWOContrast,
  ThoracicSpineVesselsMRIAngiogramWOContrast: C80DocTypecodes_ThoracicSpineVesselsMRIAngiogramWOContrast,
  TibiaBilateralXRay10DegreeCaudalAngle: C80DocTypecodes_TibiaBilateralXRay10DegreeCaudalAngle,
  TibiaLeftXRay10DegreeCaudalAngle: C80DocTypecodes_TibiaLeftXRay10DegreeCaudalAngle,
  SpineLumbosacralJunctionXRayLateralSpot: C80DocTypecodes_SpineLumbosacralJunctionXRayLateralSpot,
  SpineLumbosacralJunctionXRayLateralSpotStanding: C80DocTypecodes_SpineLumbosacralJunctionXRayLateralSpotStanding,
  FingerFifthBilateralXRay: C80DocTypecodes_FingerFifthBilateralXRay,
  FingerFifthLeftXRay: C80DocTypecodes_FingerFifthLeftXRay,
  FingerFourthBilateralXRay: C80DocTypecodes_FingerFourthBilateralXRay,
  FingerFourthLeftXRay: C80DocTypecodes_FingerFourthLeftXRay,
  FingerSecondBilateralXRay: C80DocTypecodes_FingerSecondBilateralXRay,
  FingerSecondLeftXRay: C80DocTypecodes_FingerSecondLeftXRay,
  FingerThirdBilateralXRay: C80DocTypecodes_FingerThirdBilateralXRay,
  FingerThirdLeftXRay: C80DocTypecodes_FingerThirdLeftXRay,
  ToeFifthLeftXRay: C80DocTypecodes_ToeFifthLeftXRay,
  ToeFourthLeftXRay: C80DocTypecodes_ToeFourthLeftXRay,
  GreatToeBilateralXRay: C80DocTypecodes_GreatToeBilateralXRay,
  GreatToeLeftXRay: C80DocTypecodes_GreatToeLeftXRay,
  ToeSecondLeftXRay: C80DocTypecodes_ToeSecondLeftXRay,
  ToeThirdLeftXRay: C80DocTypecodes_ToeThirdLeftXRay,
  ShoulderLeftXRayGrasheyAndAxillaryAndY: C80DocTypecodes_ShoulderLeftXRayGrasheyAndAxillaryAndY,
  BreastMammogramGrid: C80DocTypecodes_BreastMammogramGrid,
  KneeBilateralXRayHolmbladStanding: C80DocTypecodes_KneeBilateralXRayHolmbladStanding,
  MastoidBilateralXRayLawAndMayerAndStenverAndTowne: C80DocTypecodes_MastoidBilateralXRayLawAndMayerAndStenverAndTowne,
  BreastMammogramMagnification: C80DocTypecodes_BreastMammogramMagnification,
  BreastBilateralMammogramMagnification: C80DocTypecodes_BreastBilateralMammogramMagnification,
  WristBilateralXRayOblique: C80DocTypecodes_WristBilateralXRayOblique,
  HipLeftXRayObliqueCrosstable: C80DocTypecodes_HipLeftXRayObliqueCrosstable,
  TemporomandibularJointBilateralXRayOpenAndClosedMouth: C80DocTypecodes_TemporomandibularJointBilateralXRayOpenAndClosedMouth,
  WristBilateralXRayPAAndLateral: C80DocTypecodes_WristBilateralXRayPAAndLateral,
  WristLeftXRayPAAndLateral: C80DocTypecodes_WristLeftXRayPAAndLateral,
  WristBilateralXRayPAAndLateralAndOblique: C80DocTypecodes_WristBilateralXRayPAAndLateralAndOblique,
  WristLeftXRayPAAndLateralAndOblique: C80DocTypecodes_WristLeftXRayPAAndLateralAndOblique,
  BreastMammogramSpot: C80DocTypecodes_BreastMammogramSpot,
  BreastBilateralMammogramSpot: C80DocTypecodes_BreastBilateralMammogramSpot,
  BreastLeftMammogramSpotCompression: C80DocTypecodes_BreastLeftMammogramSpotCompression,
  BreastBilateralMammogramMagnificationAndSpot: C80DocTypecodes_BreastBilateralMammogramMagnificationAndSpot,
  WristLeftXRayUlnarDeviationAndRadialDeviation: C80DocTypecodes_WristLeftXRayUlnarDeviationAndRadialDeviation,
  AnkleXRayWManualStress: C80DocTypecodes_AnkleXRayWManualStress,
  AnkleBilateralXRayWManualStress: C80DocTypecodes_AnkleBilateralXRayWManualStress,
  AnkleLeftXRayWManualStress: C80DocTypecodes_AnkleLeftXRayWManualStress,
  FootLeftXRayWManualStress: C80DocTypecodes_FootLeftXRayWManualStress,
  KneeXRayWManualStress: C80DocTypecodes_KneeXRayWManualStress,
  KneeBilateralXRayWManualStress: C80DocTypecodes_KneeBilateralXRayWManualStress,
  KneeLeftXRayWManualStress: C80DocTypecodes_KneeLeftXRayWManualStress,
  ThumbBilateralXRayWManualStress: C80DocTypecodes_ThumbBilateralXRayWManualStress,
  ThumbLeftXRayWManualStress: C80DocTypecodes_ThumbLeftXRayWManualStress,
  UnspecifiedBodyRegionFluoroscopyWBariumContrastViaFistula: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyWBariumContrastViaFistula,
  UnspecifiedBodyRegionFluoroscopyWContrastViaCatheter: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyWContrastViaCatheter,
  ColonFluoroscopyWContrastViaColostomy: C80DocTypecodes_ColonFluoroscopyWContrastViaColostomy,
  UnspecifiedBodyRegionFluoroscopyWContrastViaFistula: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyWContrastViaFistula,
  UrinaryBladderFluoroscopyWContrastViaSuprapubicTube: C80DocTypecodes_UrinaryBladderFluoroscopyWContrastViaSuprapubicTube,
  WristBilateralFluoroscopyWContrastIntraarticular: C80DocTypecodes_WristBilateralFluoroscopyWContrastIntraarticular,
  WristLeftFluoroscopyWContrastIntraarticular: C80DocTypecodes_WristLeftFluoroscopyWContrastIntraarticular,
  SpineFluoroscopyWContrastIT: C80DocTypecodes_SpineFluoroscopyWContrastIT,
  LowerExtremityVesselsFluoroscopicAngiogramWContrastIV: C80DocTypecodes_LowerExtremityVesselsFluoroscopicAngiogramWContrastIV,
  GallbladderXRayWContrastAndFattyMealPO: C80DocTypecodes_GallbladderXRayWContrastAndFattyMealPO,
  UnspecifiedBodyRegionFluoroscopyWGastrografinViaFistula: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyWGastrografinViaFistula,
  AcromioclavicularJointXRayWWeight: C80DocTypecodes_AcromioclavicularJointXRayWWeight,
  AcromioclavicularJointBilateralXRayWWeight: C80DocTypecodes_AcromioclavicularJointBilateralXRayWWeight,
  AcromioclavicularJointXRayWAndWOWeight: C80DocTypecodes_AcromioclavicularJointXRayWAndWOWeight,
  AcromioclavicularJointBilateralXRayWAndWOWeight: C80DocTypecodes_AcromioclavicularJointBilateralXRayWAndWOWeight,
  AcromioclavicularJointLeftXRayWAndWOWeight: C80DocTypecodes_AcromioclavicularJointLeftXRayWAndWOWeight,
  AcromioclavicularJointXRayWOWeight: C80DocTypecodes_AcromioclavicularJointXRayWOWeight,
  PelvisAndHipBilateralXRayAndLateralFrog: C80DocTypecodes_PelvisAndHipBilateralXRayAndLateralFrog,
  GreatToeLeftXRayStanding: C80DocTypecodes_GreatToeLeftXRayStanding,
  JejunumFluoroscopyWContrast: C80DocTypecodes_JejunumFluoroscopyWContrast,
  PenisFluoroscopyWContrastIntraCorpusCavernosum: C80DocTypecodes_PenisFluoroscopyWContrastIntraCorpusCavernosum,
  AorticArchAndCarotidArteryBilateralAndVertebralArteryBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndCarotidArteryBilateralAndVertebralArteryBilateralFluoroscopicAngiogramWContrastIA,
  AorticArchAndCarotidArteryCommonBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndCarotidArteryCommonBilateralFluoroscopicAngiogramWContrastIA,
  AorticArchAndCarotidArteryCommonLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndCarotidArteryCommonLeftFluoroscopicAngiogramWContrastIA,
  AorticArchAndCarotidArteryCommonRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndCarotidArteryCommonRightFluoroscopicAngiogramWContrastIA,
  AorticArchAndCarotidArteryExternalBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndCarotidArteryExternalBilateralFluoroscopicAngiogramWContrastIA,
  AorticArchAndCarotidArteryExternalLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndCarotidArteryExternalLeftFluoroscopicAngiogramWContrastIA,
  AorticArchAndCarotidArteryExternalRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndCarotidArteryExternalRightFluoroscopicAngiogramWContrastIA,
  AorticArchAndCarotidArteryAndVertebralArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndCarotidArteryAndVertebralArteryFluoroscopicAngiogramWContrastIA,
  CoronaryGraftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CoronaryGraftFluoroscopicAngiogramWContrastIA,
  LymphaticsAbdominalAndLymphaticsPelvicLeftFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_LymphaticsAbdominalAndLymphaticsPelvicLeftFluoroscopyWContrastIntraLymphatic,
  LymphaticsAbdominalAndLymphaticsPelvicFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_LymphaticsAbdominalAndLymphaticsPelvicFluoroscopyWContrastIntraLymphatic,
  LymphaticsAbdominalAndLymphaticsPelvicBilateralFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_LymphaticsAbdominalAndLymphaticsPelvicBilateralFluoroscopyWContrastIntraLymphatic,
  ExtremityLymphaticsLeftFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_ExtremityLymphaticsLeftFluoroscopyWContrastIntraLymphatic,
  LymphaticsLeftFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_LymphaticsLeftFluoroscopyWContrastIntraLymphatic,
  LymphaticsPelvicBilateralFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_LymphaticsPelvicBilateralFluoroscopyWContrastIntraLymphatic,
  AdrenalVeinLeftFluoroscopicAngiogramWContrastIV: C80DocTypecodes_AdrenalVeinLeftFluoroscopicAngiogramWContrastIV,
  RibsLeftAndChestXRayLateralAndPAChest: C80DocTypecodes_RibsLeftAndChestXRayLateralAndPAChest,
  NasalBonesXRay3Views: C80DocTypecodes_NasalBonesXRay3Views,
  NasalBonesXRayLateralAndWaters: C80DocTypecodes_NasalBonesXRayLateralAndWaters,
  NasalBonesXRayTomograph: C80DocTypecodes_NasalBonesXRayTomograph,
  KidneyXRayWContrastIV: C80DocTypecodes_KidneyXRayWContrastIV,
  USGuidanceForLocalizationOfForeignBodyOfEye: C80DocTypecodes_USGuidanceForLocalizationOfForeignBodyOfEye,
  OpticForamenXRay4Views: C80DocTypecodes_OpticForamenXRay4Views,
  OrbitBilateralXRayTomograph: C80DocTypecodes_OrbitBilateralXRayTomograph,
  OrbitBilateralXRay4Views: C80DocTypecodes_OrbitBilateralXRay4Views,
  OrbitBilateralXRayWaters: C80DocTypecodes_OrbitBilateralXRayWaters,
  PatellaXRaySingleView: C80DocTypecodes_PatellaXRaySingleView,
  PelvisVesselsFluoroscopicAngiogramWContrast: C80DocTypecodes_PelvisVesselsFluoroscopicAngiogramWContrast,
  PelvisXRaySingleView: C80DocTypecodes_PelvisXRaySingleView,
  PelvisXRay2Views: C80DocTypecodes_PelvisXRay2Views,
  PelvisXRayAPAndInlet: C80DocTypecodes_PelvisXRayAPAndInlet,
  PelvisXRayAPAndJudet: C80DocTypecodes_PelvisXRayAPAndJudet,
  PelvisXRayAPAndLateral: C80DocTypecodes_PelvisXRayAPAndLateral,
  PelvisXRayAPAndOblique: C80DocTypecodes_PelvisXRayAPAndOblique,
  PelvisXRayAPSingleView: C80DocTypecodes_PelvisXRayAPSingleView,
  PelvisXRayAPAndInletAndOutlet: C80DocTypecodes_PelvisXRayAPAndInletAndOutlet,
  PelvisXRayAPAndLateralAndOblique: C80DocTypecodes_PelvisXRayAPAndLateralAndOblique,
  PelvisXRayFerguson: C80DocTypecodes_PelvisXRayFerguson,
  PelvisXRayLateralFrog: C80DocTypecodes_PelvisXRayLateralFrog,
  PelvisXRayInletAndOutletAndOblique: C80DocTypecodes_PelvisXRayInletAndOutletAndOblique,
  PelvisXRayInlet: C80DocTypecodes_PelvisXRayInlet,
  PelvisXRayLateral: C80DocTypecodes_PelvisXRayLateral,
  PelvisXRayOblique: C80DocTypecodes_PelvisXRayOblique,
  PelvisXRayOutlet: C80DocTypecodes_PelvisXRayOutlet,
  PelvisXRayTomograph: C80DocTypecodes_PelvisXRayTomograph,
  PelvisXRayStanding: C80DocTypecodes_PelvisXRayStanding,
  PelvisXRayAP20DegreeCephalicAngle: C80DocTypecodes_PelvisXRayAP20DegreeCephalicAngle,
  AcetabulumXRay3Views: C80DocTypecodes_AcetabulumXRay3Views,
  AbdomenXRay: C80DocTypecodes_AbdomenXRay,
  ExtremityXRay: C80DocTypecodes_ExtremityXRay,
  NeckXRay: C80DocTypecodes_NeckXRay,
  RenalVesselsFluoroscopicAngiogramWContrast: C80DocTypecodes_RenalVesselsFluoroscopicAngiogramWContrast,
  WristRightFluoroscopyWContrastIntraarticular: C80DocTypecodes_WristRightFluoroscopyWContrastIntraarticular,
  WristRightXRayLimited: C80DocTypecodes_WristRightXRayLimited,
  WristRightXRayOblique: C80DocTypecodes_WristRightXRayOblique,
  WristRightXRayTomograph: C80DocTypecodes_WristRightXRayTomograph,
  WristRightXRayUlnarDeviation: C80DocTypecodes_WristRightXRayUlnarDeviation,
  SacroiliacJointXRayLimited: C80DocTypecodes_SacroiliacJointXRayLimited,
  SacroiliacJointFluoroscopyWContrastIntraarticular: C80DocTypecodes_SacroiliacJointFluoroscopyWContrastIntraarticular,
  SacroiliacJointXRay3Views: C80DocTypecodes_SacroiliacJointXRay3Views,
  SacroiliacJointXRayAPAndOblique: C80DocTypecodes_SacroiliacJointXRayAPAndOblique,
  SacroiliacJointXRayFerguson: C80DocTypecodes_SacroiliacJointXRayFerguson,
  SacrumXRay2Views: C80DocTypecodes_SacrumXRay2Views,
  SacrumXRayAPAndLateral: C80DocTypecodes_SacrumXRayAPAndLateral,
  SacrumXRayTomograph: C80DocTypecodes_SacrumXRayTomograph,
  ScapulaXRaySingleView: C80DocTypecodes_ScapulaXRaySingleView,
  ScapulaXRay2Views: C80DocTypecodes_ScapulaXRay2Views,
  ScapulaXRayY: C80DocTypecodes_ScapulaXRayY,
  SpineThoracicAndLumbarXRay2ViewsScoliosis: C80DocTypecodes_SpineThoracicAndLumbarXRay2ViewsScoliosis,
  SpineThoracicAndLumbarXRayScoliosisAPStanding: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPStanding,
  SpineThoracicAndLumbarXRayScoliosisLateralStanding: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisLateralStanding,
  AcromioclavicularJointRightXRay2Views: C80DocTypecodes_AcromioclavicularJointRightXRay2Views,
  AcromioclavicularJointRightXRayAPSingleView: C80DocTypecodes_AcromioclavicularJointRightXRayAPSingleView,
  AcromioclavicularJointRightXRayWAndWOWeight: C80DocTypecodes_AcromioclavicularJointRightXRayWAndWOWeight,
  AcetabulumRightXRay2Views: C80DocTypecodes_AcetabulumRightXRay2Views,
  AnkleRightXRay3Views: C80DocTypecodes_AnkleRightXRay3Views,
  AnkleRightXRayAPAndLateralAndMortise: C80DocTypecodes_AnkleRightXRayAPAndLateralAndMortise,
  AnkleRightXRayAPAndLateral: C80DocTypecodes_AnkleRightXRayAPAndLateral,
  AnkleRightXRayAPAndLateralAndOblique: C80DocTypecodes_AnkleRightXRayAPAndLateralAndOblique,
  AnkleRightXRayLateralWManualStress: C80DocTypecodes_AnkleRightXRayLateralWManualStress,
  AnkleRightXRayLateralAndMortise: C80DocTypecodes_AnkleRightXRayLateralAndMortise,
  AnkleRightXRayMortiseWManualStress: C80DocTypecodes_AnkleRightXRayMortiseWManualStress,
  AnkleRightXRay2ViewsWManualStress: C80DocTypecodes_AnkleRightXRay2ViewsWManualStress,
  AnkleRightXRayWManualStress: C80DocTypecodes_AnkleRightXRayWManualStress,
  AnkleRightXRayTomograph: C80DocTypecodes_AnkleRightXRayTomograph,
  AnkleRightXRay2ViewsStanding: C80DocTypecodes_AnkleRightXRay2ViewsStanding,
  AnkleRightXRayStanding: C80DocTypecodes_AnkleRightXRayStanding,
  WristRightXRayTunnelCarpal: C80DocTypecodes_WristRightXRayTunnelCarpal,
  WristRightXRay2ViewsTunnelCarpal: C80DocTypecodes_WristRightXRay2ViewsTunnelCarpal,
  ClavicleRightXRay2Views: C80DocTypecodes_ClavicleRightXRay2Views,
  ClavicleRightXRayAPAndSerendipity: C80DocTypecodes_ClavicleRightXRayAPAndSerendipity,
  ElbowRightXRay2Views: C80DocTypecodes_ElbowRightXRay2Views,
  ElbowRightXRay3Views: C80DocTypecodes_ElbowRightXRay3Views,
  ElbowRightXRay4Views: C80DocTypecodes_ElbowRightXRay4Views,
  ElbowRightXRayAPAndLateral: C80DocTypecodes_ElbowRightXRayAPAndLateral,
  ElbowRightXRayAPAndLateralAndOblique: C80DocTypecodes_ElbowRightXRayAPAndLateralAndOblique,
  ElbowRightXRay2ViewsOblique: C80DocTypecodes_ElbowRightXRay2ViewsOblique,
  ElbowRightXRayOblique: C80DocTypecodes_ElbowRightXRayOblique,
  ElbowRightXRayTomograph: C80DocTypecodes_ElbowRightXRayTomograph,
  FemurRightXRaySingleView: C80DocTypecodes_FemurRightXRaySingleView,
  FemurRightXRay2Views: C80DocTypecodes_FemurRightXRay2Views,
  FemurRightXRay4Views: C80DocTypecodes_FemurRightXRay4Views,
  FemurRightXRayAPAndLateral: C80DocTypecodes_FemurRightXRayAPAndLateral,
  FemurRightXRayStanding: C80DocTypecodes_FemurRightXRayStanding,
  FingerRightXRay2Views: C80DocTypecodes_FingerRightXRay2Views,
  FingerRightXRay3Views: C80DocTypecodes_FingerRightXRay3Views,
  FingerRightXRayAPAndLateralAndOblique: C80DocTypecodes_FingerRightXRayAPAndLateralAndOblique,
  FootRightXRay2Views: C80DocTypecodes_FootRightXRay2Views,
  FootRightXRay2ViewsStanding: C80DocTypecodes_FootRightXRay2ViewsStanding,
  FootRightXRay3Views: C80DocTypecodes_FootRightXRay3Views,
  FootRightXRay3ViewsStanding: C80DocTypecodes_FootRightXRay3ViewsStanding,
  FootRightXRayAPAndLateral: C80DocTypecodes_FootRightXRayAPAndLateral,
  FootRightXRayAPAndLateralAndOblique: C80DocTypecodes_FootRightXRayAPAndLateralAndOblique,
  FootRightXRayLateral: C80DocTypecodes_FootRightXRayLateral,
  FootRightXRayObliqueSingleView: C80DocTypecodes_FootRightXRayObliqueSingleView,
  FootRightXRayWManualStress: C80DocTypecodes_FootRightXRayWManualStress,
  FootRightXRayTomograph: C80DocTypecodes_FootRightXRayTomograph,
  RadiusRightAndUlnaRightXRay2Views: C80DocTypecodes_RadiusRightAndUlnaRightXRay2Views,
  RadiusRightAndUlnaRightXRayAPAndLateral: C80DocTypecodes_RadiusRightAndUlnaRightXRayAPAndLateral,
  RadiusRightAndUlnaRightXRayOblique: C80DocTypecodes_RadiusRightAndUlnaRightXRayOblique,
  HandRightXRayAPAndLateral: C80DocTypecodes_HandRightXRayAPAndLateral,
  HandRightXRayAPAndLateralAndOblique: C80DocTypecodes_HandRightXRayAPAndLateralAndOblique,
  HandRightXRayLateral: C80DocTypecodes_HandRightXRayLateral,
  HandRightXRayPAAndLateral: C80DocTypecodes_HandRightXRayPAAndLateral,
  HandRightXRayPA: C80DocTypecodes_HandRightXRayPA,
  HandRightXRayPAAndLateralAndOblique: C80DocTypecodes_HandRightXRayPAAndLateralAndOblique,
  HandRightXRay: C80DocTypecodes_HandRightXRay,
  HandRightXRayTomograph: C80DocTypecodes_HandRightXRayTomograph,
  CalcaneusRightXRay2Views: C80DocTypecodes_CalcaneusRightXRay2Views,
  CalcaneusRightXRayAPAndLateral: C80DocTypecodes_CalcaneusRightXRayAPAndLateral,
  CalcaneusRightXRayStanding: C80DocTypecodes_CalcaneusRightXRayStanding,
  HipRightXRay2Views: C80DocTypecodes_HipRightXRay2Views,
  HipRightXRay3Views: C80DocTypecodes_HipRightXRay3Views,
  HipRightXRayAPAndLateralCrosstable: C80DocTypecodes_HipRightXRayAPAndLateralCrosstable,
  HipRightXRayAPAndLateralFrog: C80DocTypecodes_HipRightXRayAPAndLateralFrog,
  HipRightXRayAPAndLateral: C80DocTypecodes_HipRightXRayAPAndLateral,
  HipRightXRayAPSingleView: C80DocTypecodes_HipRightXRayAPSingleView,
  HipRightXRayLateralCrosstable: C80DocTypecodes_HipRightXRayLateralCrosstable,
  HipRightXRayObliqueCrosstable: C80DocTypecodes_HipRightXRayObliqueCrosstable,
  HipRightXRayLateralFrog: C80DocTypecodes_HipRightXRayLateralFrog,
  HipRightXRayLateral: C80DocTypecodes_HipRightXRayLateral,
  HipRightXRayStanding: C80DocTypecodes_HipRightXRayStanding,
  HipRightXRayJudet: C80DocTypecodes_HipRightXRayJudet,
  LowerExtremityRightXRayAPSingleViewStanding: C80DocTypecodes_LowerExtremityRightXRayAPSingleViewStanding,
  LowerExtremityRightXRayStanding: C80DocTypecodes_LowerExtremityRightXRayStanding,
  HipRightXRayTomograph: C80DocTypecodes_HipRightXRayTomograph,
  HumerusRightXRayAPAndLateral: C80DocTypecodes_HumerusRightXRayAPAndLateral,
  HumerusRightXRayOblique: C80DocTypecodes_HumerusRightXRayOblique,
  HumerusRightXRay: C80DocTypecodes_HumerusRightXRay,
  IliacArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_IliacArteryRightFluoroscopicAngiogramWContrastIA,
  KneeRightXRayAPAndLateralAndSunriseAndTunnel: C80DocTypecodes_KneeRightXRayAPAndLateralAndSunriseAndTunnel,
  KneeRightXRaySingleView: C80DocTypecodes_KneeRightXRaySingleView,
  KneeRightXRay3Views: C80DocTypecodes_KneeRightXRay3Views,
  KneeRightXRay4Views: C80DocTypecodes_KneeRightXRay4Views,
  KneeRightXRay5Views: C80DocTypecodes_KneeRightXRay5Views,
  KneeRightXRayAPAndLateral: C80DocTypecodes_KneeRightXRayAPAndLateral,
  KneeRightXRayAPWManualStress: C80DocTypecodes_KneeRightXRayAPWManualStress,
  KneeRightXRayAPAndLateralAndTunnel: C80DocTypecodes_KneeRightXRayAPAndLateralAndTunnel,
  KneeRightXRayAPAndLateralAndOblique: C80DocTypecodes_KneeRightXRayAPAndLateralAndOblique,
  KneeRightXRayAPAndLateralAndSunrise: C80DocTypecodes_KneeRightXRayAPAndLateralAndSunrise,
  KneeRightXRayLateralHyperextension: C80DocTypecodes_KneeRightXRayLateralHyperextension,
  KneeRightXRayLateral: C80DocTypecodes_KneeRightXRayLateral,
  KneeRightXRayRosenbergStanding: C80DocTypecodes_KneeRightXRayRosenbergStanding,
  KneeRightXRayWManualStress: C80DocTypecodes_KneeRightXRayWManualStress,
  KneeRightXRayLateralStanding: C80DocTypecodes_KneeRightXRayLateralStanding,
  KneeRightXRayPAStanding: C80DocTypecodes_KneeRightXRayPAStanding,
  KneeRightXRayTunnelStanding: C80DocTypecodes_KneeRightXRayTunnelStanding,
  KneeRightXRayOblique: C80DocTypecodes_KneeRightXRayOblique,
  KneeRightXRay: C80DocTypecodes_KneeRightXRay,
  KneeRightXRaySunriseAndTunnel: C80DocTypecodes_KneeRightXRaySunriseAndTunnel,
  KneeRightXRayTomograph: C80DocTypecodes_KneeRightXRayTomograph,
  KneeRightXRayTunnel: C80DocTypecodes_KneeRightXRayTunnel,
  KneeRightXRay2ViewsStanding: C80DocTypecodes_KneeRightXRay2ViewsStanding,
  KneeRightXRay4ViewsStanding: C80DocTypecodes_KneeRightXRay4ViewsStanding,
  LowerExtremityRightXRaySingleView: C80DocTypecodes_LowerExtremityRightXRaySingleView,
  LowerExtremityVesselsRightFluoroscopicAngiogramWContrast: C80DocTypecodes_LowerExtremityVesselsRightFluoroscopicAngiogramWContrast,
  LowerExtremityRightXRayTomograph: C80DocTypecodes_LowerExtremityRightXRayTomograph,
  LowerExtremityVeinsRightFluoroscopicAngiogramWContrastIV: C80DocTypecodes_LowerExtremityVeinsRightFluoroscopicAngiogramWContrastIV,
  BreastRightMammogram2Views: C80DocTypecodes_BreastRightMammogram2Views,
  BreastRightMammogramMagnificationAndSpot: C80DocTypecodes_BreastRightMammogramMagnificationAndSpot,
  BreastRightMammogramTangential: C80DocTypecodes_BreastRightMammogramTangential,
  BreastRightMammogramTrueLateral: C80DocTypecodes_BreastRightMammogramTrueLateral,
  BreastRightMammogramXCCL: C80DocTypecodes_BreastRightMammogramXCCL,
  BreastRightMammogramMagnification: C80DocTypecodes_BreastRightMammogramMagnification,
  BreastRightMammogram: C80DocTypecodes_BreastRightMammogram,
  BreastRightMammogramRoll: C80DocTypecodes_BreastRightMammogramRoll,
  PatellaRightXRayAPAndLateral: C80DocTypecodes_PatellaRightXRayAPAndLateral,
  PatellaRightXRay: C80DocTypecodes_PatellaRightXRay,
  PoplitealArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_PoplitealArteryRightFluoroscopicAngiogramWContrastIA,
  PulmonaryArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_PulmonaryArteryRightFluoroscopicAngiogramWContrastIA,
  RibsRightXRay2Views: C80DocTypecodes_RibsRightXRay2Views,
  RibsRightXRay3Views: C80DocTypecodes_RibsRightXRay3Views,
  RibsRightXRayAnteriorAndLateral: C80DocTypecodes_RibsRightXRayAnteriorAndLateral,
  RibsRightXRayAPSingleView: C80DocTypecodes_RibsRightXRayAPSingleView,
  RibsRightXRayLateral: C80DocTypecodes_RibsRightXRayLateral,
  SacroiliacJointRightFluoroscopyWContrastIntraarticular: C80DocTypecodes_SacroiliacJointRightFluoroscopyWContrastIntraarticular,
  SacroiliacJointRightXRay: C80DocTypecodes_SacroiliacJointRightXRay,
  ScapulaRightXRay2Views: C80DocTypecodes_ScapulaRightXRay2Views,
  ScapulaRightXRayAPAndLateral: C80DocTypecodes_ScapulaRightXRayAPAndLateral,
  ScapulaRightXRayAPAndY: C80DocTypecodes_ScapulaRightXRayAPAndY,
  ScapulaRightXRayY: C80DocTypecodes_ScapulaRightXRayY,
  ShoulderRightXRayStrykerNotch: C80DocTypecodes_ShoulderRightXRayStrykerNotch,
  ShoulderRightXRaySingleView: C80DocTypecodes_ShoulderRightXRaySingleView,
  ShoulderRightXRay2Views: C80DocTypecodes_ShoulderRightXRay2Views,
  ShoulderRightXRay4Views: C80DocTypecodes_ShoulderRightXRay4Views,
  ShoulderRightXRay5Views: C80DocTypecodes_ShoulderRightXRay5Views,
  ShoulderRightXRay6Views: C80DocTypecodes_ShoulderRightXRay6Views,
  ShoulderRightXRayAPAndStrykerNotch: C80DocTypecodes_ShoulderRightXRayAPAndStrykerNotch,
  ShoulderRightXRayAPSingleView: C80DocTypecodes_ShoulderRightXRayAPSingleView,
  ShoulderRightXRayAPAndWestPointAndOutlet: C80DocTypecodes_ShoulderRightXRayAPAndWestPointAndOutlet,
  ShoulderRightXRayAxillary: C80DocTypecodes_ShoulderRightXRayAxillary,
  ShoulderRightXRayGarth: C80DocTypecodes_ShoulderRightXRayGarth,
  ShoulderRightXRayOutlet: C80DocTypecodes_ShoulderRightXRayOutlet,
  ShoulderRightXRayLateralAndY: C80DocTypecodes_ShoulderRightXRayLateralAndY,
  ShoulderRightXRayOutletAndY: C80DocTypecodes_ShoulderRightXRayOutletAndY,
  ShoulderRightXRayY: C80DocTypecodes_ShoulderRightXRayY,
  ShoulderRightXRayGrasheyAndAxillaryAndOutlet: C80DocTypecodes_ShoulderRightXRayGrasheyAndAxillaryAndOutlet,
  ShoulderRightXRayAxillaryAndY: C80DocTypecodes_ShoulderRightXRayAxillaryAndY,
  SternoclavicularJointRightXRaySerendipity: C80DocTypecodes_SternoclavicularJointRightXRaySerendipity,
  ShoulderRightXRayWestPoint: C80DocTypecodes_ShoulderRightXRayWestPoint,
  AcromioclavicularJointRightXRayZanca: C80DocTypecodes_AcromioclavicularJointRightXRayZanca,
  ShoulderRightXRayTomograph: C80DocTypecodes_ShoulderRightXRayTomograph,
  ThumbRightXRay3Views: C80DocTypecodes_ThumbRightXRay3Views,
  ThumbRightXRayAPAndLateralAndOblique: C80DocTypecodes_ThumbRightXRayAPAndLateralAndOblique,
  ThumbRightXRayWManualStress: C80DocTypecodes_ThumbRightXRayWManualStress,
  TibiaRightAndFibulaRightXRay2Views: C80DocTypecodes_TibiaRightAndFibulaRightXRay2Views,
  TibiaRightAndFibulaRightXRayAPAndLateral: C80DocTypecodes_TibiaRightAndFibulaRightXRayAPAndLateral,
  TibiaRightAndFibulaRightXRayOblique: C80DocTypecodes_TibiaRightAndFibulaRightXRayOblique,
  TemporomandibularJointRightFluoroscopyWContrastIntraarticular: C80DocTypecodes_TemporomandibularJointRightFluoroscopyWContrastIntraarticular,
  TemporomandibularJointRightXRayTomograph: C80DocTypecodes_TemporomandibularJointRightXRayTomograph,
  ToesRightXRay3Views: C80DocTypecodes_ToesRightXRay3Views,
  ToesRightXRay2Views: C80DocTypecodes_ToesRightXRay2Views,
  ToesRightXRayAPAndLateral: C80DocTypecodes_ToesRightXRayAPAndLateral,
  ToesRightXRayStanding: C80DocTypecodes_ToesRightXRayStanding,
  UpperExtremityVeinsRightFluoroscopicAngiogramWContrastIV: C80DocTypecodes_UpperExtremityVeinsRightFluoroscopicAngiogramWContrastIV,
  WristRightXRaySingleView: C80DocTypecodes_WristRightXRaySingleView,
  WristRightXRay2Views: C80DocTypecodes_WristRightXRay2Views,
  WristRightXRay3Views: C80DocTypecodes_WristRightXRay3Views,
  WristRightXRay4Views: C80DocTypecodes_WristRightXRay4Views,
  WristRightXRay5Views: C80DocTypecodes_WristRightXRay5Views,
  WristRightXRay6Views: C80DocTypecodes_WristRightXRay6Views,
  WristRightXRay8Views: C80DocTypecodes_WristRightXRay8Views,
  WristRightXRayAPAndLateral: C80DocTypecodes_WristRightXRayAPAndLateral,
  WristRightXRayLateralWExtension: C80DocTypecodes_WristRightXRayLateralWExtension,
  WristRightXRayLateralWFlexion: C80DocTypecodes_WristRightXRayLateralWFlexion,
  WristRightXRayPAAndLateral: C80DocTypecodes_WristRightXRayPAAndLateral,
  WristRightXRayPAAndLateralAndOblique: C80DocTypecodes_WristRightXRayPAAndLateralAndOblique,
  ShoulderXRayAPAndLateralAndAxillary: C80DocTypecodes_ShoulderXRayAPAndLateralAndAxillary,
  ShoulderXRay2Views: C80DocTypecodes_ShoulderXRay2Views,
  ShoulderXRayAPAndLateral: C80DocTypecodes_ShoulderXRayAPAndLateral,
  ShoulderXRayAPSingleView: C80DocTypecodes_ShoulderXRayAPSingleView,
  ShoulderXRayGarth: C80DocTypecodes_ShoulderXRayGarth,
  ShoulderXRayGrashey: C80DocTypecodes_ShoulderXRayGrashey,
  ShoulderXRayOutlet: C80DocTypecodes_ShoulderXRayOutlet,
  SternoclavicularJointXRaySerendipity: C80DocTypecodes_SternoclavicularJointXRaySerendipity,
  ShoulderXRayY: C80DocTypecodes_ShoulderXRayY,
  AcromioclavicularJointXRayZanca: C80DocTypecodes_AcromioclavicularJointXRayZanca,
  ShoulderXRayAxillary: C80DocTypecodes_ShoulderXRayAxillary,
  ShoulderXRayTomograph: C80DocTypecodes_ShoulderXRayTomograph,
  SinusesXRaySingleView: C80DocTypecodes_SinusesXRaySingleView,
  SinusesXRayCaldwellAndWaters: C80DocTypecodes_SinusesXRayCaldwellAndWaters,
  SinusesXRay2Views: C80DocTypecodes_SinusesXRay2Views,
  SinusesXRay3Views: C80DocTypecodes_SinusesXRay3Views,
  SinusesXRay4Views: C80DocTypecodes_SinusesXRay4Views,
  SinusesXRay5Views: C80DocTypecodes_SinusesXRay5Views,
  SinusesXRayCaldwell: C80DocTypecodes_SinusesXRayCaldwell,
  SinusesXRayLateral: C80DocTypecodes_SinusesXRayLateral,
  SinusesXRayPAAndLateralAndWaters: C80DocTypecodes_SinusesXRayPAAndLateralAndWaters,
  SinusesXRayPAAndLateralAndCaldwellAndWaters: C80DocTypecodes_SinusesXRayPAAndLateralAndCaldwellAndWaters,
  SinusesXRaySubmentovertex: C80DocTypecodes_SinusesXRaySubmentovertex,
  SinusesXRayLateralAndWaters: C80DocTypecodes_SinusesXRayLateralAndWaters,
  SinusesXRayWaters: C80DocTypecodes_SinusesXRayWaters,
  SinusesXRayLateralAndCaldwellAndWaters: C80DocTypecodes_SinusesXRayLateralAndCaldwellAndWaters,
  SinusesXRayTomograph: C80DocTypecodes_SinusesXRayTomograph,
  SkullXRay2Views: C80DocTypecodes_SkullXRay2Views,
  SkullXRay4Views: C80DocTypecodes_SkullXRay4Views,
  SkullXRayLateralAndTowne: C80DocTypecodes_SkullXRayLateralAndTowne,
  SkullXRayTowne: C80DocTypecodes_SkullXRayTowne,
  SkullXRayLateralAndCaldwellAndWatersAndTowne: C80DocTypecodes_SkullXRayLateralAndCaldwellAndWatersAndTowne,
  SkullXRayLateralCrosstable: C80DocTypecodes_SkullXRayLateralCrosstable,
  SkullXRayTomograph: C80DocTypecodes_SkullXRayTomograph,
  SpineXRaySingleView: C80DocTypecodes_SpineXRaySingleView,
  SpineXRay4Views: C80DocTypecodes_SpineXRay4Views,
  SpineXRayAPSingleView: C80DocTypecodes_SpineXRayAPSingleView,
  SpineXRayLateralCrosstable: C80DocTypecodes_SpineXRayLateralCrosstable,
  SpineXRay2Views: C80DocTypecodes_SpineXRay2Views,
  SternoclavicularJointXRayAPSingleView: C80DocTypecodes_SternoclavicularJointXRayAPSingleView,
  SternoclavicularJointXRay3Views: C80DocTypecodes_SternoclavicularJointXRay3Views,
  SternoclavicularJointXRay4Views: C80DocTypecodes_SternoclavicularJointXRay4Views,
  SternumXRay2Views: C80DocTypecodes_SternumXRay2Views,
  SternumXRayPAAndLateralAndOblique: C80DocTypecodes_SternumXRayPAAndLateralAndOblique,
  SternumXRayTomograph: C80DocTypecodes_SternumXRayTomograph,
  SubclavianArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_SubclavianArteryFluoroscopicAngiogramWContrastIA,
  FluoroscopyGuidanceForAspirationOfPleuralSpace: C80DocTypecodes_FluoroscopyGuidanceForAspirationOfPleuralSpace,
  ThumbXRay3Views: C80DocTypecodes_ThumbXRay3Views,
  ThumbXRayAPAndLateral: C80DocTypecodes_ThumbXRayAPAndLateral,
  ThumbXRayAPSingleView: C80DocTypecodes_ThumbXRayAPSingleView,
  ThumbXRayLateral: C80DocTypecodes_ThumbXRayLateral,
  ThumbXRayObliqueSingleView: C80DocTypecodes_ThumbXRayObliqueSingleView,
  TibiaAndFibulaXRayLateral: C80DocTypecodes_TibiaAndFibulaXRayLateral,
  TibiaAndFibulaXRaySingleView: C80DocTypecodes_TibiaAndFibulaXRaySingleView,
  TibiaAndFibulaXRay2Views: C80DocTypecodes_TibiaAndFibulaXRay2Views,
  TibiaAndFibulaXRayAPAndLateral: C80DocTypecodes_TibiaAndFibulaXRayAPAndLateral,
  TibiaAndFibulaXRayAPSingleView: C80DocTypecodes_TibiaAndFibulaXRayAPSingleView,
  TibiaAndFibulaXRayTomograph: C80DocTypecodes_TibiaAndFibulaXRayTomograph,
  TibiaAndFibulaXRayStanding: C80DocTypecodes_TibiaAndFibulaXRayStanding,
  TibialArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_TibialArteryFluoroscopicAngiogramWContrastIA,
  TemporomandibularJointFluoroscopyWContrastIntraarticular: C80DocTypecodes_TemporomandibularJointFluoroscopyWContrastIntraarticular,
  ToesXRay2Views: C80DocTypecodes_ToesXRay2Views,
  SpineThoracicXRayLateralCrosstable: C80DocTypecodes_SpineThoracicXRayLateralCrosstable,
  SpineThoracicXRaySingleView: C80DocTypecodes_SpineThoracicXRaySingleView,
  SpineThoracicXRay2Views: C80DocTypecodes_SpineThoracicXRay2Views,
  SpineThoracicXRay3Views: C80DocTypecodes_SpineThoracicXRay3Views,
  SpineThoracicXRay4Views: C80DocTypecodes_SpineThoracicXRay4Views,
  SpineThoracicXRayAPAndLateralAndOblique: C80DocTypecodes_SpineThoracicXRayAPAndLateralAndOblique,
  SpineThoracicXRayLateralHyperextension: C80DocTypecodes_SpineThoracicXRayLateralHyperextension,
  SpineThoracicXRayLateralStanding: C80DocTypecodes_SpineThoracicXRayLateralStanding,
  SpineThoracicXRayTomograph: C80DocTypecodes_SpineThoracicXRayTomograph,
  USGuidanceForBiopsyOfBreastBilateral: C80DocTypecodes_USGuidanceForBiopsyOfBreastBilateral,
  USGuidanceForBiopsyOfAbdomen: C80DocTypecodes_USGuidanceForBiopsyOfAbdomen,
  USGuidanceForBiopsyOfBreast: C80DocTypecodes_USGuidanceForBiopsyOfBreast,
  USGuidanceForBiopsyOfChest: C80DocTypecodes_USGuidanceForBiopsyOfChest,
  USGuidanceForNeedleBiopsyOfLymphNode: C80DocTypecodes_USGuidanceForNeedleBiopsyOfLymphNode,
  USGuidanceForBiopsyOfMuscle: C80DocTypecodes_USGuidanceForBiopsyOfMuscle,
  USGuidanceForBiopsyOfNeck: C80DocTypecodes_USGuidanceForBiopsyOfNeck,
  USGuidanceForBiopsyOfPancreas: C80DocTypecodes_USGuidanceForBiopsyOfPancreas,
  USGuidanceForBiopsyOfSalivaryGland: C80DocTypecodes_USGuidanceForBiopsyOfSalivaryGland,
  USGuidanceForNeedleLocalizationOfChest: C80DocTypecodes_USGuidanceForNeedleLocalizationOfChest,
  UpperExtremityXRay2Views: C80DocTypecodes_UpperExtremityXRay2Views,
  UpperExtremityXRayTomograph: C80DocTypecodes_UpperExtremityXRayTomograph,
  WristXRaySingleView: C80DocTypecodes_WristXRaySingleView,
  WristXRay2Views: C80DocTypecodes_WristXRay2Views,
  WristXRay3Views: C80DocTypecodes_WristXRay3Views,
  WristXRayAPAndLateralAndOblique: C80DocTypecodes_WristXRayAPAndLateralAndOblique,
  WristXRayBrewerton: C80DocTypecodes_WristXRayBrewerton,
  WristXRayLateralWFlexionAndWExtension: C80DocTypecodes_WristXRayLateralWFlexionAndWExtension,
  WristXRayLateral: C80DocTypecodes_WristXRayLateral,
  WristXRayPA: C80DocTypecodes_WristXRayPA,
  WristXRayTomograph: C80DocTypecodes_WristXRayTomograph,
  ZygomaticArchXRay3Views: C80DocTypecodes_ZygomaticArchXRay3Views,
  ZygomaticArchXRay4Views: C80DocTypecodes_ZygomaticArchXRay4Views,
  PelvisArteriesAndLowerExtremityArteriesBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_PelvisArteriesAndLowerExtremityArteriesBilateralFluoroscopicAngiogramWContrastIA,
  PeripheralVesselsFluoroscopicAngiogramWContrast: C80DocTypecodes_PeripheralVesselsFluoroscopicAngiogramWContrast,
  RibsAnteriorXRay: C80DocTypecodes_RibsAnteriorXRay,
  RibsPosteriorXRay: C80DocTypecodes_RibsPosteriorXRay,
  AdrenalArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AdrenalArteryRightFluoroscopicAngiogramWContrastIA,
  AdrenalVeinRightFluoroscopicAngiogramWContrastIV: C80DocTypecodes_AdrenalVeinRightFluoroscopicAngiogramWContrastIV,
  AnkleArteriesRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AnkleArteriesRightFluoroscopicAngiogramWContrastIA,
  AnkleRightFluoroscopyWContrastIntraarticular: C80DocTypecodes_AnkleRightFluoroscopyWContrastIntraarticular,
  CarotidArteryPlusVertebralArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryPlusVertebralArteryRightFluoroscopicAngiogramWContrastIA,
  CarotidArteryAndCerebralArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryAndCerebralArteryRightFluoroscopicAngiogramWContrastIA,
  CarotidArteryCervicalRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryCervicalRightFluoroscopicAngiogramWContrastIA,
  ElbowRightFluoroscopyWContrastIntraarticular: C80DocTypecodes_ElbowRightFluoroscopyWContrastIntraarticular,
  CarotidArteryExternalRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryExternalRightFluoroscopicAngiogramWContrastIA,
  ExtremityArteriesRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_ExtremityArteriesRightFluoroscopicAngiogramWContrastIA,
  ExtremityVeinsRightFluoroscopicAngiogramWContrastIV: C80DocTypecodes_ExtremityVeinsRightFluoroscopicAngiogramWContrastIV,
  CarotidArteryInternalRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryInternalRightFluoroscopicAngiogramWContrastIA,
  CarotidArteryAndCerebralArteryInternalRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryAndCerebralArteryInternalRightFluoroscopicAngiogramWContrastIA,
  JugularVeinRightFluoroscopicAngiogramWContrastIV: C80DocTypecodes_JugularVeinRightFluoroscopicAngiogramWContrastIV,
  OrbitVeinsRightFluoroscopicAngiogramWContrastIV: C80DocTypecodes_OrbitVeinsRightFluoroscopicAngiogramWContrastIV,
  RenalVeinRightFluoroscopicAngiogramWContrastIV: C80DocTypecodes_RenalVeinRightFluoroscopicAngiogramWContrastIV,
  RibsLowerRightXRay: C80DocTypecodes_RibsLowerRightXRay,
  RibsUpperRightXRay: C80DocTypecodes_RibsUpperRightXRay,
  RibsAnteriorAndPosteriorRightXRay: C80DocTypecodes_RibsAnteriorAndPosteriorRightXRay,
  RibsAnteriorRightXRay: C80DocTypecodes_RibsAnteriorRightXRay,
  RibsPosteriorRightXRay: C80DocTypecodes_RibsPosteriorRightXRay,
  SternoclavicularJointRightXRay: C80DocTypecodes_SternoclavicularJointRightXRay,
  SubclavianArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_SubclavianArteryRightFluoroscopicAngiogramWContrastIA,
  UpperExtremityArteriesRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_UpperExtremityArteriesRightFluoroscopicAngiogramWContrastIA,
  VertebralArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_VertebralArteryRightFluoroscopicAngiogramWContrastIA,
  SinusVeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_SinusVeinFluoroscopicAngiogramWContrastIV,
  SplenicVeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_SplenicVeinFluoroscopicAngiogramWContrastIV,
  SubclavianVeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_SubclavianVeinFluoroscopicAngiogramWContrastIV,
  SuperiorMesentericVeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_SuperiorMesentericVeinFluoroscopicAngiogramWContrastIV,
  TesticleVesselsFluoroscopyWContrast: C80DocTypecodes_TesticleVesselsFluoroscopyWContrast,
  SpineThoracolumbarJunctionXRayAPAndLateral: C80DocTypecodes_SpineThoracolumbarJunctionXRayAPAndLateral,
  SpineThoracolumbarJunctionXRay: C80DocTypecodes_SpineThoracolumbarJunctionXRay,
  UpperExtremityVesselsFluoroscopicAngiogramWContrast: C80DocTypecodes_UpperExtremityVesselsFluoroscopicAngiogramWContrast,
  UpperExtremityArteriesFluoroscopicAngiogramWContrastIA: C80DocTypecodes_UpperExtremityArteriesFluoroscopicAngiogramWContrastIA,
  UterineArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_UterineArteryFluoroscopicAngiogramWContrastIA,
  VertebralVesselsFluoroscopicAngiogramWContrast: C80DocTypecodes_VertebralVesselsFluoroscopicAngiogramWContrast,
  VisceralVesselsFluoroscopicAngiogramWContrast: C80DocTypecodes_VisceralVesselsFluoroscopicAngiogramWContrast,
  LumbarSpineVesselsMRIAngiogramWOContrast: C80DocTypecodes_LumbarSpineVesselsMRIAngiogramWOContrast,
  CalcaneusBilateralXRayBroden: C80DocTypecodes_CalcaneusBilateralXRayBroden,
  CalcaneusXRayBroden: C80DocTypecodes_CalcaneusXRayBroden,
  CalcaneusLeftXRayBroden: C80DocTypecodes_CalcaneusLeftXRayBroden,
  ElbowXRayRadialHeadCapitellar: C80DocTypecodes_ElbowXRayRadialHeadCapitellar,
  ElbowBilateralXRayRadialHeadCapitellar: C80DocTypecodes_ElbowBilateralXRayRadialHeadCapitellar,
  ElbowLeftXRayRadialHeadCapitellar: C80DocTypecodes_ElbowLeftXRayRadialHeadCapitellar,
  ChestXRayWExpiration: C80DocTypecodes_ChestXRayWExpiration,
  ChestXRayWInspiration: C80DocTypecodes_ChestXRayWInspiration,
  FootLeftXRayAPStanding: C80DocTypecodes_FootLeftXRayAPStanding,
  ShoulderLeftXRayGrasheyWAndWOWeight: C80DocTypecodes_ShoulderLeftXRayGrasheyWAndWOWeight,
  ElbowRightXRayRadialHeadCapitellar: C80DocTypecodes_ElbowRightXRayRadialHeadCapitellar,
  HumerusRightXRayTransthoracic: C80DocTypecodes_HumerusRightXRayTransthoracic,
  SpineCervicalAndThoracicAndLumbarXRay: C80DocTypecodes_SpineCervicalAndThoracicAndLumbarXRay,
  SpineThoracicXRayAPAndLateralAndSwimmers: C80DocTypecodes_SpineThoracicXRayAPAndLateralAndSwimmers,
  SpineThoracicXRayLateralWFlexionAndWExtension: C80DocTypecodes_SpineThoracicXRayLateralWFlexionAndWExtension,
  AortaUSLimited: C80DocTypecodes_AortaUSLimited,
  USGuidanceForAspirationOfCystOfBreastBilateral: C80DocTypecodes_USGuidanceForAspirationOfCystOfBreastBilateral,
  LowerExtremityBilateralUS: C80DocTypecodes_LowerExtremityBilateralUS,
  UpperExtremityArteryBilateralUS: C80DocTypecodes_UpperExtremityArteryBilateralUS,
  CarotidArteryUSLimited: C80DocTypecodes_CarotidArteryUSLimited,
  ChestWallUS: C80DocTypecodes_ChestWallUS,
  USGuidanceForFineNeedleAspirationOfProstate: C80DocTypecodes_USGuidanceForFineNeedleAspirationOfProstate,
  USGuidanceForFineNeedleAspirationOfUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForFineNeedleAspirationOfUnspecifiedBodyRegion,
  USGuidanceForFineNeedleAspirationOfThyroid: C80DocTypecodes_USGuidanceForFineNeedleAspirationOfThyroid,
  GallbladderUSLimited: C80DocTypecodes_GallbladderUSLimited,
  BiliaryDuctsAndGallbladderUS: C80DocTypecodes_BiliaryDuctsAndGallbladderUS,
  GallbladderUSWCholecystokinin: C80DocTypecodes_GallbladderUSWCholecystokinin,
  USGuidanceForCoreNeedlePercutaneousBiopsyOfBreastLeft: C80DocTypecodes_USGuidanceForCoreNeedlePercutaneousBiopsyOfBreastLeft,
  USGuidanceForCoreNeedleBiopsyOfUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForCoreNeedleBiopsyOfUnspecifiedBodyRegion,
  USGuidanceForCoreNeedlePercutaneousBiopsyOfBreastRight: C80DocTypecodes_USGuidanceForCoreNeedlePercutaneousBiopsyOfBreastRight,
  USGuidanceForFineNeedleAspirationOfBreastLeft: C80DocTypecodes_USGuidanceForFineNeedleAspirationOfBreastLeft,
  USGuidanceForNeedleBiopsyOfKidneyBilateral: C80DocTypecodes_USGuidanceForNeedleBiopsyOfKidneyBilateral,
  USGuidanceForNeedleBiopsyOfBreast: C80DocTypecodes_USGuidanceForNeedleBiopsyOfBreast,
  USGuidanceForNeedleBiopsyOfChest: C80DocTypecodes_USGuidanceForNeedleBiopsyOfChest,
  USGuidanceForNeedleBiopsyOfSpleen: C80DocTypecodes_USGuidanceForNeedleBiopsyOfSpleen,
  USGuidanceForNeedleBiopsyOfThyroid: C80DocTypecodes_USGuidanceForNeedleBiopsyOfThyroid,
  USGuidanceForNeedleLocalizationOfUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForNeedleLocalizationOfUnspecifiedBodyRegion,
  USGuidanceForFineNeedleAspirationOfBreastRight: C80DocTypecodes_USGuidanceForFineNeedleAspirationOfBreastRight,
  HeadUSLimited: C80DocTypecodes_HeadUSLimited,
  KidneyUSLimited: C80DocTypecodes_KidneyUSLimited,
  KidneyUS: C80DocTypecodes_KidneyUS,
  FemurLeftUS: C80DocTypecodes_FemurLeftUS,
  KidneyLeftUS: C80DocTypecodes_KidneyLeftUS,
  LowerExtremityLeftUSLimited: C80DocTypecodes_LowerExtremityLeftUSLimited,
  LowerExtremityLeftUS: C80DocTypecodes_LowerExtremityLeftUS,
  UpperExtremityLeftUS: C80DocTypecodes_UpperExtremityLeftUS,
  LowerExtremityArteryUSDopplerLimited: C80DocTypecodes_LowerExtremityArteryUSDopplerLimited,
  MastoidUS: C80DocTypecodes_MastoidUS,
  MediastinumUS: C80DocTypecodes_MediastinumUS,
  ParathyroidUS: C80DocTypecodes_ParathyroidUS,
  PelvisUSLimited: C80DocTypecodes_PelvisUSLimited,
  AbdomenRetroperitoneumUSLimited: C80DocTypecodes_AbdomenRetroperitoneumUSLimited,
  FemurRightUS: C80DocTypecodes_FemurRightUS,
  KidneyRightUS: C80DocTypecodes_KidneyRightUS,
  LowerExtremityRightUSLimited: C80DocTypecodes_LowerExtremityRightUSLimited,
  LowerExtremityRightUS: C80DocTypecodes_LowerExtremityRightUS,
  UpperExtremityRightUS: C80DocTypecodes_UpperExtremityRightUS,
  SacrumUS: C80DocTypecodes_SacrumUS,
  VisceralArteryUS: C80DocTypecodes_VisceralArteryUS,
  UnspecifiedBodyRegionUSWContrast: C80DocTypecodes_UnspecifiedBodyRegionUSWContrast,
  FDAPackageInsertStructuredProductLaballingSupplementalPatientMaterial: C80DocTypecodes_FDAPackageInsertStructuredProductLaballingSupplementalPatientMaterial,
  BreastImplantLeftMRI: C80DocTypecodes_BreastImplantLeftMRI,
  BreastImplantRightMRI: C80DocTypecodes_BreastImplantRightMRI,
  TalusCT: C80DocTypecodes_TalusCT,
  SpineLumbosacralPlusCervicalPlusThoracicMRISagittal: C80DocTypecodes_SpineLumbosacralPlusCervicalPlusThoracicMRISagittal,
  SpineCervicalAndSpineThoracicAndSpineLumbarAndSacrumMRIWContrastIV: C80DocTypecodes_SpineCervicalAndSpineThoracicAndSpineLumbarAndSacrumMRIWContrastIV,
  BreastImplantRightMRIWAndWOContrastIV: C80DocTypecodes_BreastImplantRightMRIWAndWOContrastIV,
  BreastImplantLeftMRIWOContrast: C80DocTypecodes_BreastImplantLeftMRIWOContrast,
  HipLeftXRayDuringSurgery: C80DocTypecodes_HipLeftXRayDuringSurgery,
  HipLeftXRayLateralDuringSurgery: C80DocTypecodes_HipLeftXRayLateralDuringSurgery,
  BreastBilateralMammogramNippleProfile: C80DocTypecodes_BreastBilateralMammogramNippleProfile,
  ChestXRayRightAnteriorOblique: C80DocTypecodes_ChestXRayRightAnteriorOblique,
  AbdomenXRayLeftPosteriorOblique: C80DocTypecodes_AbdomenXRayLeftPosteriorOblique,
  BreastImplantMammogram: C80DocTypecodes_BreastImplantMammogram,
  BreastImplantBilateralMammogram: C80DocTypecodes_BreastImplantBilateralMammogram,
  BreastImplantLeftMammogram: C80DocTypecodes_BreastImplantLeftMammogram,
  RibsAnteriorBilateralXRay: C80DocTypecodes_RibsAnteriorBilateralXRay,
  RibsAnteriorLeftXRay: C80DocTypecodes_RibsAnteriorLeftXRay,
  BreastSpecimenBilateralMammogram: C80DocTypecodes_BreastSpecimenBilateralMammogram,
  BreastSpecimenLeftMammogram: C80DocTypecodes_BreastSpecimenLeftMammogram,
  ShoulderLeftXRayAPAndTransthoracic: C80DocTypecodes_ShoulderLeftXRayAPAndTransthoracic,
  SpineCervicalXRayAPAndLateralAndObliqueAndOdontoidAndSwimmer: C80DocTypecodes_SpineCervicalXRayAPAndLateralAndObliqueAndOdontoidAndSwimmer,
  AbdomenXRayAPAndLeftPosteriorOblique: C80DocTypecodes_AbdomenXRayAPAndLeftPosteriorOblique,
  KneeXRayMerchants30And45And60Degrees: C80DocTypecodes_KneeXRayMerchants30And45And60Degrees,
  KneeLeftXRaySunrise20And40And60Degrees: C80DocTypecodes_KneeLeftXRaySunrise20And40And60Degrees,
  KneeBilateralXRaySunrise20And40And60Degrees: C80DocTypecodes_KneeBilateralXRaySunrise20And40And60Degrees,
  BonesXRaySurveyLimitedForMetastasis: C80DocTypecodes_BonesXRaySurveyLimitedForMetastasis,
  BreastBilateralMammogramWAir: C80DocTypecodes_BreastBilateralMammogramWAir,
  BreastLeftMammogramWAir: C80DocTypecodes_BreastLeftMammogramWAir,
  UrinaryBladderFluoroscopyWChainAndContrastIntraBladder: C80DocTypecodes_UrinaryBladderFluoroscopyWChainAndContrastIntraBladder,
  ChestXRayWNippleMarkers: C80DocTypecodes_ChestXRayWNippleMarkers,
  SpineCavityFluoroscopyWContrast: C80DocTypecodes_SpineCavityFluoroscopyWContrast,
  BreastDuctBilateralMammogramWContrastIntraDuct: C80DocTypecodes_BreastDuctBilateralMammogramWContrastIntraDuct,
  BreastDuctLeftMammogramWContrastIntraDuct: C80DocTypecodes_BreastDuctLeftMammogramWContrastIntraDuct,
  ParotidGlandLeftFluoroscopyWContrastIntraSalivaryDuct: C80DocTypecodes_ParotidGlandLeftFluoroscopyWContrastIntraSalivaryDuct,
  LacrimalDuctBilateralFluoroscopyWContrastIntraLacrimalDuct: C80DocTypecodes_LacrimalDuctBilateralFluoroscopyWContrastIntraLacrimalDuct,
  LacrimalDuctLeftFluoroscopyWContrastIntraLacrimalDuct: C80DocTypecodes_LacrimalDuctLeftFluoroscopyWContrastIntraLacrimalDuct,
  UrinaryBladderAndUrethraFluoroscopyWContrastAntegrade: C80DocTypecodes_UrinaryBladderAndUrethraFluoroscopyWContrastAntegrade,
  KidneyXRayWContrastAntegrade: C80DocTypecodes_KidneyXRayWContrastAntegrade,
  KidneyXRayWContrastAntegradeViaPyelostomy: C80DocTypecodes_KidneyXRayWContrastAntegradeViaPyelostomy,
  SpineCervicalAndSpineLumbarFluoroscopyWContrastIT: C80DocTypecodes_SpineCervicalAndSpineLumbarFluoroscopyWContrastIT,
  SpineEpiduralSpaceFluoroscopyWContrastIT: C80DocTypecodes_SpineEpiduralSpaceFluoroscopyWContrastIT,
  KidneyXRayWContrastRetrograde: C80DocTypecodes_KidneyXRayWContrastRetrograde,
  WristXRayScaphoid: C80DocTypecodes_WristXRayScaphoid,
  KneeRightXRay2ViewsOblique: C80DocTypecodes_KneeRightXRay2ViewsOblique,
  KidneyRightAndCollectingSystemFluoroscopyWContrastViaNephrostomyTube: C80DocTypecodes_KidneyRightAndCollectingSystemFluoroscopyWContrastViaNephrostomyTube,
  KidneyRightAndCollectingSystemFluoroscopyWContrastRetrogradeViaUrethra: C80DocTypecodes_KidneyRightAndCollectingSystemFluoroscopyWContrastRetrogradeViaUrethra,
  TibiaRightAndFibulaRightXRay2ViewsOblique: C80DocTypecodes_TibiaRightAndFibulaRightXRay2ViewsOblique,
  WristRightXRayScaphoid: C80DocTypecodes_WristRightXRayScaphoid,
  ParotidGlandFluoroscopyWContrastIntraSalivaryDuct: C80DocTypecodes_ParotidGlandFluoroscopyWContrastIntraSalivaryDuct,
  SinusesXRayWatersUpright: C80DocTypecodes_SinusesXRayWatersUpright,
  NeckXRay2ViewsLateral: C80DocTypecodes_NeckXRay2ViewsLateral,
  ThoracicArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_ThoracicArteryFluoroscopicAngiogramWContrastIA,
  SpineThoracicFluoroscopyLimitedWContrastIT: C80DocTypecodes_SpineThoracicFluoroscopyLimitedWContrastIT,
  SpineThoracicAndLumbarXRaySingleView: C80DocTypecodes_SpineThoracicAndLumbarXRaySingleView,
  SpineThoracicAndLumbarXRayAPAndLateral: C80DocTypecodes_SpineThoracicAndLumbarXRayAPAndLateral,
  SpineThoracicAndLumbarXRayStanding: C80DocTypecodes_SpineThoracicAndLumbarXRayStanding,
  SpineCervicalAndThoracicAndLumbarFluoroscopyLimitedWContrastIT: C80DocTypecodes_SpineCervicalAndThoracicAndLumbarFluoroscopyLimitedWContrastIT,
  USGuidanceForAspirationOfCystOfKidney: C80DocTypecodes_USGuidanceForAspirationOfCystOfKidney,
  USGuidanceForCSFAspirationOfSpine: C80DocTypecodes_USGuidanceForCSFAspirationOfSpine,
  FemoralVesselsBilateralUS: C80DocTypecodes_FemoralVesselsBilateralUS,
  IliacVesselsBilateralUS: C80DocTypecodes_IliacVesselsBilateralUS,
  LowerExtremityArteryBilateralUS: C80DocTypecodes_LowerExtremityArteryBilateralUS,
  SubclavianVesselsBilateralUS: C80DocTypecodes_SubclavianVesselsBilateralUS,
  USGuidanceForBiopsyOfScrotumAndTesticle: C80DocTypecodes_USGuidanceForBiopsyOfScrotumAndTesticle,
  USGuidanceForAspirationOfCystOfPancreas: C80DocTypecodes_USGuidanceForAspirationOfCystOfPancreas,
  FemoralVesselsUS: C80DocTypecodes_FemoralVesselsUS,
  USGuidanceForFineNeedleAspirationOfDeepTissue: C80DocTypecodes_USGuidanceForFineNeedleAspirationOfDeepTissue,
  USGuidanceForFineNeedleAspirationOfSuperficialTissue: C80DocTypecodes_USGuidanceForFineNeedleAspirationOfSuperficialTissue,
  IliacVesselsLeftUS: C80DocTypecodes_IliacVesselsLeftUS,
  ParotidGlandUS: C80DocTypecodes_ParotidGlandUS,
  PenisVesselsUS: C80DocTypecodes_PenisVesselsUS,
  PenisUS: C80DocTypecodes_PenisUS,
  IliacVesselsRightUS: C80DocTypecodes_IliacVesselsRightUS,
  USGuidanceForRemovalOfFluidFromChest: C80DocTypecodes_USGuidanceForRemovalOfFluidFromChest,
  UpperExtremityArteryUSDopplerLimited: C80DocTypecodes_UpperExtremityArteryUSDopplerLimited,
  FingerSecondRightXRay: C80DocTypecodes_FingerSecondRightXRay,
  FingerThirdRightXRay: C80DocTypecodes_FingerThirdRightXRay,
  FingerFourthRightXRay: C80DocTypecodes_FingerFourthRightXRay,
  FingerFifthRightXRay: C80DocTypecodes_FingerFifthRightXRay,
  ToeSecondRightXRay: C80DocTypecodes_ToeSecondRightXRay,
  ToeThirdRightXRay: C80DocTypecodes_ToeThirdRightXRay,
  ToeFourthRightXRay: C80DocTypecodes_ToeFourthRightXRay,
  ToeFifthRightXRay: C80DocTypecodes_ToeFifthRightXRay,
  GreatToeRightXRay: C80DocTypecodes_GreatToeRightXRay,
  SubmandibularGlandFluoroscopyWContrastIntraSalivaryDuct: C80DocTypecodes_SubmandibularGlandFluoroscopyWContrastIntraSalivaryDuct,
  FluoroscopyGuidanceForBiopsyOfSuperficialBone: C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfSuperficialBone,
  WristXRay4Views: C80DocTypecodes_WristXRay4Views,
  WristXRay6Views: C80DocTypecodes_WristXRay6Views,
  SkeletalSystemDXABoneDensity: C80DocTypecodes_SkeletalSystemDXABoneDensity,
  StudyReportSkeletalSystemDXA: C80DocTypecodes_StudyReportSkeletalSystemDXA,
  USGuidanceForBiopsyOfLiverTransplant: C80DocTypecodes_USGuidanceForBiopsyOfLiverTransplant,
  USGuidanceForBiopsyOfKidneyTransplant: C80DocTypecodes_USGuidanceForBiopsyOfKidneyTransplant,
  InternalAuditoryCanalRightCT: C80DocTypecodes_InternalAuditoryCanalRightCT,
  FemurRightXRayTomograph: C80DocTypecodes_FemurRightXRayTomograph,
  LowerExtremityJointRightMRILimitedWOContrast: C80DocTypecodes_LowerExtremityJointRightMRILimitedWOContrast,
  ScapulaRightMRIWOContrast: C80DocTypecodes_ScapulaRightMRIWOContrast,
  PelvisAndHipRightXRay: C80DocTypecodes_PelvisAndHipRightXRay,
  HipRightXRayTrueLateral: C80DocTypecodes_HipRightXRayTrueLateral,
  LowerExtremityVesselsRightMRIAngiogramWOContrast: C80DocTypecodes_LowerExtremityVesselsRightMRIAngiogramWOContrast,
  OrbitRightXRay: C80DocTypecodes_OrbitRightXRay,
  HandRightXRayBrewerton: C80DocTypecodes_HandRightXRayBrewerton,
  CalcaneusRightXRayHarris: C80DocTypecodes_CalcaneusRightXRayHarris,
  ElbowRightXRayJones: C80DocTypecodes_ElbowRightXRayJones,
  CalcaneusRightXRaySkiJump: C80DocTypecodes_CalcaneusRightXRaySkiJump,
  ShoulderRightXRayTransthoracic: C80DocTypecodes_ShoulderRightXRayTransthoracic,
  ShoulderRightXRayVelpeauAxillary: C80DocTypecodes_ShoulderRightXRayVelpeauAxillary,
  ShoulderRightXRayAPAndAxillaryAndOutlet: C80DocTypecodes_ShoulderRightXRayAPAndAxillaryAndOutlet,
  ShoulderRightXRayAPAndAxillaryAndOutletAndZanca: C80DocTypecodes_ShoulderRightXRayAPAndAxillaryAndOutletAndZanca,
  ShoulderRightXRayAPAndAxillaryAndY: C80DocTypecodes_ShoulderRightXRayAPAndAxillaryAndY,
  PelvisAndHipRightXRayAPAndLateralCrosstable: C80DocTypecodes_PelvisAndHipRightXRayAPAndLateralCrosstable,
  PelvisAndHipRightXRayAPAndLateralFrog: C80DocTypecodes_PelvisAndHipRightXRayAPAndLateralFrog,
  PatellaRightXRayAPAndLateralAndSunrise: C80DocTypecodes_PatellaRightXRayAPAndLateralAndSunrise,
  ShoulderRightXRayAPAndWestPoint: C80DocTypecodes_ShoulderRightXRayAPAndWestPoint,
  ShoulderRightXRayAPAndY: C80DocTypecodes_ShoulderRightXRayAPAndY,
  ShoulderRightXRayGrasheyAndAxillaryAndY: C80DocTypecodes_ShoulderRightXRayGrasheyAndAxillaryAndY,
  PatellaRightXRayPAAndLateralAndSunrise: C80DocTypecodes_PatellaRightXRayPAAndLateralAndSunrise,
  ShoulderRightXRayGrasheyAndOutlet: C80DocTypecodes_ShoulderRightXRayGrasheyAndOutlet,
  FootRightXRayTarsal: C80DocTypecodes_FootRightXRayTarsal,
  ShoulderRightXRayGrasheyAndAxillary: C80DocTypecodes_ShoulderRightXRayGrasheyAndAxillary,
  ShoulderRightXRayGrasheyAndOutletAndSerendipity: C80DocTypecodes_ShoulderRightXRayGrasheyAndOutletAndSerendipity,
  ShoulderRightXRayGrasheyAndWestPoint: C80DocTypecodes_ShoulderRightXRayGrasheyAndWestPoint,
  HipRightXRayPortable: C80DocTypecodes_HipRightXRayPortable,
  HumerusBicipitalGrooveRightXRay: C80DocTypecodes_HumerusBicipitalGrooveRightXRay,
  OlecranonRightXRay: C80DocTypecodes_OlecranonRightXRay,
  AortaAndFemoralArteryRightFluoroscopicAngiogramRunoffWContrastIA: C80DocTypecodes_AortaAndFemoralArteryRightFluoroscopicAngiogramRunoffWContrastIA,
  AorticArchAndSubclavianArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchAndSubclavianArteryRightFluoroscopicAngiogramWContrastIA,
  GastricArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_GastricArteryRightFluoroscopicAngiogramWContrastIA,
  WristRightCTWAndWOContrastIV: C80DocTypecodes_WristRightCTWAndWOContrastIV,
  ClavicleRightXRay45DegreeCephalicAngle: C80DocTypecodes_ClavicleRightXRay45DegreeCephalicAngle,
  HandRightXRayBora: C80DocTypecodes_HandRightXRayBora,
  ShoulderRightXRayGrashey: C80DocTypecodes_ShoulderRightXRayGrashey,
  TibiaRightXRay10DegreeCaudalAngle: C80DocTypecodes_TibiaRightXRay10DegreeCaudalAngle,
  BreastRightMammogramSpot: C80DocTypecodes_BreastRightMammogramSpot,
  WristRightXRayUlnarDeviationAndRadialDeviation: C80DocTypecodes_WristRightXRayUlnarDeviationAndRadialDeviation,
  GreatToeRightXRayStanding: C80DocTypecodes_GreatToeRightXRayStanding,
  LymphaticsAbdominalAndLymphaticsPelvicRightFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_LymphaticsAbdominalAndLymphaticsPelvicRightFluoroscopyWContrastIntraLymphatic,
  ExtremityLymphaticsRightFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_ExtremityLymphaticsRightFluoroscopyWContrastIntraLymphatic,
  LymphaticsRightFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_LymphaticsRightFluoroscopyWContrastIntraLymphatic,
  CalcaneusRightXRayBroden: C80DocTypecodes_CalcaneusRightXRayBroden,
  FootRightXRayAPStanding: C80DocTypecodes_FootRightXRayAPStanding,
  ShoulderRightXRayGrasheyWAndWOWeight: C80DocTypecodes_ShoulderRightXRayGrasheyWAndWOWeight,
  BreastImplantRightMRIWOContrast: C80DocTypecodes_BreastImplantRightMRIWOContrast,
  HipRightXRayDuringSurgery: C80DocTypecodes_HipRightXRayDuringSurgery,
  HipRightXRayLateralDuringSurgery: C80DocTypecodes_HipRightXRayLateralDuringSurgery,
  BreastImplantRightMammogram: C80DocTypecodes_BreastImplantRightMammogram,
  BreastSpecimenRightMammogram: C80DocTypecodes_BreastSpecimenRightMammogram,
  ShoulderRightXRayAPAndTransthoracic: C80DocTypecodes_ShoulderRightXRayAPAndTransthoracic,
  KneeRightXRaySunrise20And40And60Degrees: C80DocTypecodes_KneeRightXRaySunrise20And40And60Degrees,
  BreastDuctRightMammogramWContrastIntraDuct: C80DocTypecodes_BreastDuctRightMammogramWContrastIntraDuct,
  ParotidGlandRightFluoroscopyWContrastIntraSalivaryDuct: C80DocTypecodes_ParotidGlandRightFluoroscopyWContrastIntraSalivaryDuct,
  LacrimalDuctRightFluoroscopyWContrastIntraLacrimalDuct: C80DocTypecodes_LacrimalDuctRightFluoroscopyWContrastIntraLacrimalDuct,
  ShoulderLeftCTWContrastIntraarticular: C80DocTypecodes_ShoulderLeftCTWContrastIntraarticular,
  UpperExtremityLeftMRIWContrastIV: C80DocTypecodes_UpperExtremityLeftMRIWContrastIV,
  ShoulderLeftMRIWContrastIV: C80DocTypecodes_ShoulderLeftMRIWContrastIV,
  UpperExtremityLeftMRIWAndWOContrastIV: C80DocTypecodes_UpperExtremityLeftMRIWAndWOContrastIV,
  UpperExtremityLeftMRIWOContrast: C80DocTypecodes_UpperExtremityLeftMRIWOContrast,
  ThoracicOutletLeftMRIWOContrast: C80DocTypecodes_ThoracicOutletLeftMRIWOContrast,
  ShoulderLeftMRIWOContrast: C80DocTypecodes_ShoulderLeftMRIWOContrast,
  TemporalBoneLeftCTWContrastIV: C80DocTypecodes_TemporalBoneLeftCTWContrastIV,
  OrbitLeftMRI: C80DocTypecodes_OrbitLeftMRI,
  KneeVesselsLeftMRIAngiogramWAndWOContrastIV: C80DocTypecodes_KneeVesselsLeftMRIAngiogramWAndWOContrastIV,
  WristLeftXRayLimited: C80DocTypecodes_WristLeftXRayLimited,
  WristLeftXRayOblique: C80DocTypecodes_WristLeftXRayOblique,
  AnkleLeftXRay2ViewsWManualStress: C80DocTypecodes_AnkleLeftXRay2ViewsWManualStress,
  AnkleLeftXRay2ViewsStanding: C80DocTypecodes_AnkleLeftXRay2ViewsStanding,
  WristLeftXRayTunnelCarpal: C80DocTypecodes_WristLeftXRayTunnelCarpal,
  WristLeftXRay2ViewsTunnelCarpal: C80DocTypecodes_WristLeftXRay2ViewsTunnelCarpal,
  ElbowLeftXRay2ViewsOblique: C80DocTypecodes_ElbowLeftXRay2ViewsOblique,
  FemurLeftXRayStanding: C80DocTypecodes_FemurLeftXRayStanding,
  FootLeftXRay2Views: C80DocTypecodes_FootLeftXRay2Views,
  HandLeftXRayAPAndLateral: C80DocTypecodes_HandLeftXRayAPAndLateral,
  HandLeftXRayAPAndLateralAndOblique: C80DocTypecodes_HandLeftXRayAPAndLateralAndOblique,
  LowerExtremityLeftXRayAPSingleViewStanding: C80DocTypecodes_LowerExtremityLeftXRayAPSingleViewStanding,
  LowerExtremityLeftXRayStanding: C80DocTypecodes_LowerExtremityLeftXRayStanding,
  KneeLeftXRay2ViewsStanding: C80DocTypecodes_KneeLeftXRay2ViewsStanding,
  KneeLeftXRay4ViewsStanding: C80DocTypecodes_KneeLeftXRay4ViewsStanding,
  LowerExtremityVesselsLeftFluoroscopicAngiogramWContrast: C80DocTypecodes_LowerExtremityVesselsLeftFluoroscopicAngiogramWContrast,
  BreastLeftMammogramMagnificationAndSpot: C80DocTypecodes_BreastLeftMammogramMagnificationAndSpot,
  BreastLeftMammogramTrueLateral: C80DocTypecodes_BreastLeftMammogramTrueLateral,
  RibsLeftXRayAnteriorAndLateral: C80DocTypecodes_RibsLeftXRayAnteriorAndLateral,
  RibsLeftXRayLateral: C80DocTypecodes_RibsLeftXRayLateral,
  ShoulderLeftXRayY: C80DocTypecodes_ShoulderLeftXRayY,
  UpperExtremityVeinsLeftFluoroscopicAngiogramWContrastIV: C80DocTypecodes_UpperExtremityVeinsLeftFluoroscopicAngiogramWContrastIV,
  WristLeftXRayAPAndLateral: C80DocTypecodes_WristLeftXRayAPAndLateral,
  AnkleArteriesLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AnkleArteriesLeftFluoroscopicAngiogramWContrastIA,
  CarotidArteryAndCerebralArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryAndCerebralArteryLeftFluoroscopicAngiogramWContrastIA,
  CarotidArteryCervicalLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryCervicalLeftFluoroscopicAngiogramWContrastIA,
  CarotidArteryInternalLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryInternalLeftFluoroscopicAngiogramWContrastIA,
  CarotidArteryAndCerebralArteryInternalLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryAndCerebralArteryInternalLeftFluoroscopicAngiogramWContrastIA,
  RibsLowerLeftXRay: C80DocTypecodes_RibsLowerLeftXRay,
  RibsUpperLeftXRay: C80DocTypecodes_RibsUpperLeftXRay,
  RibsAnteriorAndPosteriorLeftXRay: C80DocTypecodes_RibsAnteriorAndPosteriorLeftXRay,
  RibsPosteriorLeftXRay: C80DocTypecodes_RibsPosteriorLeftXRay,
  BreastImplantLeftMRIWAndWOContrastIV: C80DocTypecodes_BreastImplantLeftMRIWAndWOContrastIV,
  KneeLeftXRay2ViewsOblique: C80DocTypecodes_KneeLeftXRay2ViewsOblique,
  KidneyLeftAndCollectingSystemFluoroscopyWContrastViaNephrostomyTube: C80DocTypecodes_KidneyLeftAndCollectingSystemFluoroscopyWContrastViaNephrostomyTube,
  KidneyLeftAndCollectingSystemFluoroscopyWContrastRetrogradeViaUrethra: C80DocTypecodes_KidneyLeftAndCollectingSystemFluoroscopyWContrastRetrogradeViaUrethra,
  TibiaLeftAndFibulaLeftXRay2ViewsOblique: C80DocTypecodes_TibiaLeftAndFibulaLeftXRay2ViewsOblique,
  VACompensationAndPensionCAndPExaminationAcromegaly: C80DocTypecodes_VACompensationAndPensionCAndPExaminationAcromegaly,
  VACompensationAndPensionCAndPExaminationAidAndAttendanceHousebound: C80DocTypecodes_VACompensationAndPensionCAndPExaminationAidAndAttendanceHousebound,
  VACompensationAndPensionCAndPExaminationArrhythmias: C80DocTypecodes_VACompensationAndPensionCAndPExaminationArrhythmias,
  VACompensationAndPensionCAndPExaminationMiscellaneousArteriesVeins: C80DocTypecodes_VACompensationAndPensionCAndPExaminationMiscellaneousArteriesVeins,
  VACompensationAndPensionCAndPExaminationAudio: C80DocTypecodes_VACompensationAndPensionCAndPExaminationAudio,
  VACompensationAndPensionCAndPExaminationBonesFracturesBoneDisease: C80DocTypecodes_VACompensationAndPensionCAndPExaminationBonesFracturesBoneDisease,
  VACompensationAndPensionCAndPExaminationBrainSpinalCord: C80DocTypecodes_VACompensationAndPensionCAndPExaminationBrainSpinalCord,
  VACompensationAndPensionCAndPExaminationChronicFatigueSyndrome: C80DocTypecodes_VACompensationAndPensionCAndPExaminationChronicFatigueSyndrome,
  VACompensationAndPensionCAndPExaminationColdInjuryProtocol: C80DocTypecodes_VACompensationAndPensionCAndPExaminationColdInjuryProtocol,
  VACompensationAndPensionCAndPExaminationCranialNerves: C80DocTypecodes_VACompensationAndPensionCAndPExaminationCranialNerves,
  VACompensationAndPensionCAndPExaminationCushingsSyndrome: C80DocTypecodes_VACompensationAndPensionCAndPExaminationCushingsSyndrome,
  VACompensationAndPensionCAndPExaminationDentalOral: C80DocTypecodes_VACompensationAndPensionCAndPExaminationDentalOral,
  VACompensationAndPensionCAndPExaminationDiabetesMellitus: C80DocTypecodes_VACompensationAndPensionCAndPExaminationDiabetesMellitus,
  VACompensationAndPensionCAndPExaminationMiscellaneousDigestiveConditions: C80DocTypecodes_VACompensationAndPensionCAndPExaminationMiscellaneousDigestiveConditions,
  VACompensationAndPensionCAndPExaminationEarDisease: C80DocTypecodes_VACompensationAndPensionCAndPExaminationEarDisease,
  VACompensationAndPensionCAndPExaminationMentalHealthEatingDisorders: C80DocTypecodes_VACompensationAndPensionCAndPExaminationMentalHealthEatingDisorders,
  VACompensationAndPensionCAndPExaminationMiscellaneousEndocrineDiseases: C80DocTypecodes_VACompensationAndPensionCAndPExaminationMiscellaneousEndocrineDiseases,
  VACompensationAndPensionCAndPExaminationEpilepsyNarcolepsy: C80DocTypecodes_VACompensationAndPensionCAndPExaminationEpilepsyNarcolepsy,
  VACompensationAndPensionCAndPExaminationEsophagusHiatalHernia: C80DocTypecodes_VACompensationAndPensionCAndPExaminationEsophagusHiatalHernia,
  VACompensationAndPensionCAndPExaminationEye: C80DocTypecodes_VACompensationAndPensionCAndPExaminationEye,
  VACompensationAndPensionCAndPExaminationFeet: C80DocTypecodes_VACompensationAndPensionCAndPExaminationFeet,
  VACompensationAndPensionCAndPExaminationFibromyalgia: C80DocTypecodes_VACompensationAndPensionCAndPExaminationFibromyalgia,
  VACompensationAndPensionCAndPExaminationGeneralMedical: C80DocTypecodes_VACompensationAndPensionCAndPExaminationGeneralMedical,
  VACompensationAndPensionCAndPExaminationGenitourinary: C80DocTypecodes_VACompensationAndPensionCAndPExaminationGenitourinary,
  VACompensationAndPensionCAndPExaminationDisabilityInGulfWarVeterans: C80DocTypecodes_VACompensationAndPensionCAndPExaminationDisabilityInGulfWarVeterans,
  VACompensationAndPensionCAndPExaminationGynecologicalConditionsDisordersOfTheBreast: C80DocTypecodes_VACompensationAndPensionCAndPExaminationGynecologicalConditionsDisordersOfTheBreast,
  VACompensationAndPensionCAndPExaminationHandThumbFingers: C80DocTypecodes_VACompensationAndPensionCAndPExaminationHandThumbFingers,
  VACompensationAndPensionCAndPExaminationHeart: C80DocTypecodes_VACompensationAndPensionCAndPExaminationHeart,
  VACompensationAndPensionCAndPExaminationHemicDisorders: C80DocTypecodes_VACompensationAndPensionCAndPExaminationHemicDisorders,
  VACompensationAndPensionCAndPExaminationHIVRelatedIllness: C80DocTypecodes_VACompensationAndPensionCAndPExaminationHIVRelatedIllness,
  VACompensationAndPensionCAndPExaminationHypertension: C80DocTypecodes_VACompensationAndPensionCAndPExaminationHypertension,
  VACompensationAndPensionCAndPExaminationInfectiousImmuneNutritionalDisabilities: C80DocTypecodes_VACompensationAndPensionCAndPExaminationInfectiousImmuneNutritionalDisabilities,
  VACompensationAndPensionCAndPExaminationInitialEvaluationPostTraumaticStressDisorder: C80DocTypecodes_VACompensationAndPensionCAndPExaminationInitialEvaluationPostTraumaticStressDisorder,
  VACompensationAndPensionCAndPExaminationLargeSmallIntestines: C80DocTypecodes_VACompensationAndPensionCAndPExaminationLargeSmallIntestines,
  VACompensationAndPensionCAndPExaminationExtremityJoints: C80DocTypecodes_VACompensationAndPensionCAndPExaminationExtremityJoints,
  VACompensationAndPensionCAndPExaminationLiverGallBladderPancreas: C80DocTypecodes_VACompensationAndPensionCAndPExaminationLiverGallBladderPancreas,
  VACompensationAndPensionCAndPExaminationLymphaticDisorders: C80DocTypecodes_VACompensationAndPensionCAndPExaminationLymphaticDisorders,
  VACompensationAndPensionCAndPExaminationGeneralMentalDisorders: C80DocTypecodes_VACompensationAndPensionCAndPExaminationGeneralMentalDisorders,
  VACompensationAndPensionCAndPExaminationMouthLipsTongue: C80DocTypecodes_VACompensationAndPensionCAndPExaminationMouthLipsTongue,
  VACompensationAndPensionCAndPExaminationMuscles: C80DocTypecodes_VACompensationAndPensionCAndPExaminationMuscles,
  VACompensationAndPensionCAndPExaminationMiscellaneousNeurologicalDisorders: C80DocTypecodes_VACompensationAndPensionCAndPExaminationMiscellaneousNeurologicalDisorders,
  VACompensationAndPensionCAndPExaminationNoseSinusLarynxPharynx: C80DocTypecodes_VACompensationAndPensionCAndPExaminationNoseSinusLarynxPharynx,
  VACompensationAndPensionCAndPExaminationPeripheralNerves: C80DocTypecodes_VACompensationAndPensionCAndPExaminationPeripheralNerves,
  VACompensationAndPensionCAndPExaminationPrisonerOfWarProtocol: C80DocTypecodes_VACompensationAndPensionCAndPExaminationPrisonerOfWarProtocol,
  VACompensationAndPensionCAndPExaminationPulmonaryTuberculosisMycobacterialDiseases: C80DocTypecodes_VACompensationAndPensionCAndPExaminationPulmonaryTuberculosisMycobacterialDiseases,
  VACompensationAndPensionCAndPExaminationRectumAnus: C80DocTypecodes_VACompensationAndPensionCAndPExaminationRectumAnus,
  VACompensationAndPensionCAndPExaminationResidualsOfAmputations: C80DocTypecodes_VACompensationAndPensionCAndPExaminationResidualsOfAmputations,
  VACompensationAndPensionCAndPExaminationObstructiveRestrictiveInterstitialRespiratoryDiseases: C80DocTypecodes_VACompensationAndPensionCAndPExaminationObstructiveRestrictiveInterstitialRespiratoryDiseases,
  VACompensationAndPensionCAndPExaminationMiscellaneousRespiratoryDiseases: C80DocTypecodes_VACompensationAndPensionCAndPExaminationMiscellaneousRespiratoryDiseases,
  VACompensationAndPensionCAndPExaminationReviewEvaluationPostTraumaticStressDisorder: C80DocTypecodes_VACompensationAndPensionCAndPExaminationReviewEvaluationPostTraumaticStressDisorder,
  VACompensationAndPensionCAndPExaminationScars: C80DocTypecodes_VACompensationAndPensionCAndPExaminationScars,
  VACompensationAndPensionCAndPExaminationSenseOfSmellTaste: C80DocTypecodes_VACompensationAndPensionCAndPExaminationSenseOfSmellTaste,
  VACompensationAndPensionCAndPExaminationSkinDiseasesOtherThanScars: C80DocTypecodes_VACompensationAndPensionCAndPExaminationSkinDiseasesOtherThanScars,
  VACompensationAndPensionCAndPExaminationSocialIndustrialSurvey: C80DocTypecodes_VACompensationAndPensionCAndPExaminationSocialIndustrialSurvey,
  VACompensationAndPensionCAndPExaminationSpine: C80DocTypecodes_VACompensationAndPensionCAndPExaminationSpine,
  VACompensationAndPensionCAndPExaminationStomachDuodenumPeritonealAdhesions: C80DocTypecodes_VACompensationAndPensionCAndPExaminationStomachDuodenumPeritonealAdhesions,
  VACompensationAndPensionCAndPExaminationThyroidParathyroidDiseases: C80DocTypecodes_VACompensationAndPensionCAndPExaminationThyroidParathyroidDiseases,
  CTGuidanceForNeedleLocalizationOfUnspecifiedBodyRegion: C80DocTypecodes_CTGuidanceForNeedleLocalizationOfUnspecifiedBodyRegion,
  FluoroscopyGuidanceForNeedleLocalizationOfUnspecifiedBodyRegion: C80DocTypecodes_FluoroscopyGuidanceForNeedleLocalizationOfUnspecifiedBodyRegion,
  MRIGuidanceForNeedleLocalizationOfUnspecifiedBodyRegion: C80DocTypecodes_MRIGuidanceForNeedleLocalizationOfUnspecifiedBodyRegion,
  OrbitAndFaceMRIWAndWOContrastIV: C80DocTypecodes_OrbitAndFaceMRIWAndWOContrastIV,
  VeinBilateralUS: C80DocTypecodes_VeinBilateralUS,
  ExtremityArteryBilateralUSDoppler: C80DocTypecodes_ExtremityArteryBilateralUSDoppler,
  KidneyTransplantUS: C80DocTypecodes_KidneyTransplantUS,
  UpperExtremityMRIWOContrast: C80DocTypecodes_UpperExtremityMRIWOContrast,
  UpperExtremityMRIWAndWOContrastIV: C80DocTypecodes_UpperExtremityMRIWAndWOContrastIV,
  VeinUS: C80DocTypecodes_VeinUS,
  UpperExtremityMRIWContrastIV: C80DocTypecodes_UpperExtremityMRIWContrastIV,
  OrbitAndFaceMRIWContrastIV: C80DocTypecodes_OrbitAndFaceMRIWContrastIV,
  BrachiocephalicArteryUSDoppler: C80DocTypecodes_BrachiocephalicArteryUSDoppler,
  AVFistulaUS: C80DocTypecodes_AVFistulaUS,
  ExtremityArteryUSDoppler: C80DocTypecodes_ExtremityArteryUSDoppler,
  UnspecifiedBodyRegionMRIAnd3DReconstruction: C80DocTypecodes_UnspecifiedBodyRegionMRIAnd3DReconstruction,
  HeadVesselsUSDopplerLimited: C80DocTypecodes_HeadVesselsUSDopplerLimited,
  VeinUSLimited: C80DocTypecodes_VeinUSLimited,
  PelvisCTLimitedPelvimetryWOContrast: C80DocTypecodes_PelvisCTLimitedPelvimetryWOContrast,
  HipFluoroscopyDuringSurgery: C80DocTypecodes_HipFluoroscopyDuringSurgery,
  ScapulaXRayAPSingleView: C80DocTypecodes_ScapulaXRayAPSingleView,
  SpineThoracicAndLumbarXRayAPSingleView: C80DocTypecodes_SpineThoracicAndLumbarXRayAPSingleView,
  RibsXRayAPSingleView: C80DocTypecodes_RibsXRayAPSingleView,
  ChestXRayLateral: C80DocTypecodes_ChestXRayLateral,
  SpineXRayLateral: C80DocTypecodes_SpineXRayLateral,
  RibsXRayLateral: C80DocTypecodes_RibsXRayLateral,
  BreastDuctMammogramWContrastIntraDuct: C80DocTypecodes_BreastDuctMammogramWContrastIntraDuct,
  ExtremityVeinsFluoroscopicAngiogramWContrastIV: C80DocTypecodes_ExtremityVeinsFluoroscopicAngiogramWContrastIV,
  UnspecifiedBodyRegionXRayWManualStress: C80DocTypecodes_UnspecifiedBodyRegionXRayWManualStress,
  PulmonaryArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_PulmonaryArteryFluoroscopicAngiogramWContrastIA,
  SalivaryGlandXRay: C80DocTypecodes_SalivaryGlandXRay,
  GastrointestineUpperFluoroscopyWAirAndBariumContrastPO: C80DocTypecodes_GastrointestineUpperFluoroscopyWAirAndBariumContrastPO,
  RibsXRay2Views: C80DocTypecodes_RibsXRay2Views,
  SacrumAndCoccyxXRay3Views: C80DocTypecodes_SacrumAndCoccyxXRay3Views,
  RibsXRay3Views: C80DocTypecodes_RibsXRay3Views,
  SpineLumbarXRay5ViewsWFlexionAndWExtension: C80DocTypecodes_SpineLumbarXRay5ViewsWFlexionAndWExtension,
  RibsXRayAnteriorAndLateral: C80DocTypecodes_RibsXRayAnteriorAndLateral,
  PelvisXRayAPAndInletAndOutletAndOblique: C80DocTypecodes_PelvisXRayAPAndInletAndOutletAndOblique,
  ChestFluoroscopyAPAndLateral: C80DocTypecodes_ChestFluoroscopyAPAndLateral,
  SpineCervicalAndThoracicAndLumbarXRayAPAndLateral: C80DocTypecodes_SpineCervicalAndThoracicAndLumbarXRayAPAndLateral,
  FootXRayAPAndLateralStanding: C80DocTypecodes_FootXRayAPAndLateralStanding,
  FootXRayAPAndLateral: C80DocTypecodes_FootXRayAPAndLateral,
  ChestXRayAPAndLateralAndLordotic: C80DocTypecodes_ChestXRayAPAndLateralAndLordotic,
  KneeXRayAPAndLateralAndMerchants: C80DocTypecodes_KneeXRayAPAndLateralAndMerchants,
  AnkleXRayAPAndLateralAndOblique: C80DocTypecodes_AnkleXRayAPAndLateralAndOblique,
  KneeXRayAPAndLateralAndRightObliqueAndLeftOblique: C80DocTypecodes_KneeXRayAPAndLateralAndRightObliqueAndLeftOblique,
  ChestXRayAPAndLateralAndRightObliqueAndLeftOblique: C80DocTypecodes_ChestXRayAPAndLateralAndRightObliqueAndLeftOblique,
  ToesXRayAPAndOblique: C80DocTypecodes_ToesXRayAPAndOblique,
  FootXRayAPAndOblique: C80DocTypecodes_FootXRayAPAndOblique,
  ShoulderXRayAPAndTransthoracic: C80DocTypecodes_ShoulderXRayAPAndTransthoracic,
  FingerXRayPAAndLateralAndOblique: C80DocTypecodes_FingerXRayPAAndLateralAndOblique,
  HandXRayPAAndOblique: C80DocTypecodes_HandXRayPAAndOblique,
  HepaticVeinsFluoroscopicAngiogramWContrastIV: C80DocTypecodes_HepaticVeinsFluoroscopicAngiogramWContrastIV,
  CarotidArteryCervicalFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryCervicalFluoroscopicAngiogramWContrastIA,
  CarotidArteryAndCerebralArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryAndCerebralArteryFluoroscopicAngiogramWContrastIA,
  HepaticVeinsFluoroscopicAngiogramWContrastIVAndWHemodynamics: C80DocTypecodes_HepaticVeinsFluoroscopicAngiogramWContrastIVAndWHemodynamics,
  CarotidArteryBilateralAndCerebralArteryBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryBilateralAndCerebralArteryBilateralFluoroscopicAngiogramWContrastIA,
  CarotidArteryCervicalBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_CarotidArteryCervicalBilateralFluoroscopicAngiogramWContrastIA,
  RibsBilateralAndChestXRay4ViewsAndPAChest: C80DocTypecodes_RibsBilateralAndChestXRay4ViewsAndPAChest,
  RibsRightAndChestXRayLateralAndPAChest: C80DocTypecodes_RibsRightAndChestXRayLateralAndPAChest,
  RibsAndChestXRayLateralAndPAChest: C80DocTypecodes_RibsAndChestXRayLateralAndPAChest,
  FluoroscopicAngiogramGuidanceForVascularAccessOfVessel: C80DocTypecodes_FluoroscopicAngiogramGuidanceForVascularAccessOfVessel,
  USGuidanceForVascularAccessOfUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForVascularAccessOfUnspecifiedBodyRegion,
  HeartMRICineForBloodFlowVelocityMapping: C80DocTypecodes_HeartMRICineForBloodFlowVelocityMapping,
  BoneMarrowMRIForBloodFlow: C80DocTypecodes_BoneMarrowMRIForBloodFlow,
  HeadCTPerfusionWContrastIV: C80DocTypecodes_HeadCTPerfusionWContrastIV,
  GastrointestineUpperFluoroscopyWAirContrastPO: C80DocTypecodes_GastrointestineUpperFluoroscopyWAirContrastPO,
  BreastDuctLeftMammogramWContrastIntraMultipleDucts: C80DocTypecodes_BreastDuctLeftMammogramWContrastIntraMultipleDucts,
  BreastDuctBilateralMammogramWContrastIntraMultipleDucts: C80DocTypecodes_BreastDuctBilateralMammogramWContrastIntraMultipleDucts,
  BreastDuctRightMammogramWContrastIntraMultipleDucts: C80DocTypecodes_BreastDuctRightMammogramWContrastIntraMultipleDucts,
  BreastDuctMammogramWContrastIntraMultipleDucts: C80DocTypecodes_BreastDuctMammogramWContrastIntraMultipleDucts,
  GastrointestinalSystemAndRespiratorySystemXRayForForeignBody: C80DocTypecodes_GastrointestinalSystemAndRespiratorySystemXRayForForeignBody,
  BreastFFDMammogramPostLocalization: C80DocTypecodes_BreastFFDMammogramPostLocalization,
  VasDeferensFluoroscopyWContrastIntraVasDeferens: C80DocTypecodes_VasDeferensFluoroscopyWContrastIntraVasDeferens,
  BreastFFDMammogramDiagnostic: C80DocTypecodes_BreastFFDMammogramDiagnostic,
  BreastFFDMammogramScreening: C80DocTypecodes_BreastFFDMammogramScreening,
  BreastBilateralFFDMammogramDiagnostic: C80DocTypecodes_BreastBilateralFFDMammogramDiagnostic,
  LowerExtremityMRIWAndWOContrastIV: C80DocTypecodes_LowerExtremityMRIWAndWOContrastIV,
  LowerExtremityMRIWOContrast: C80DocTypecodes_LowerExtremityMRIWOContrast,
  LowerExtremityMRIWContrastIV: C80DocTypecodes_LowerExtremityMRIWContrastIV,
  ShoulderXRayAPWInternalRotationAndWExternalRotationAndAxillary: C80DocTypecodes_ShoulderXRayAPWInternalRotationAndWExternalRotationAndAxillary,
  SpineCTWContrastIntradisc: C80DocTypecodes_SpineCTWContrastIntradisc,
  AbdomenXRayRightPosteriorOblique: C80DocTypecodes_AbdomenXRayRightPosteriorOblique,
  WristLeftXRayPAWClenchedFist: C80DocTypecodes_WristLeftXRayPAWClenchedFist,
  ShoulderLeftXRayAPWInternalRotationAndGrasheyAndAxillaryAndOutlet: C80DocTypecodes_ShoulderLeftXRayAPWInternalRotationAndGrasheyAndAxillaryAndOutlet,
  RibsLeftAndChestXRay: C80DocTypecodes_RibsLeftAndChestXRay,
  AbdomenAndFetusXRayForFetalAge: C80DocTypecodes_AbdomenAndFetusXRayForFetalAge,
  ShoulderLeftXRayAPWInternalRotationAndWExternalRotation: C80DocTypecodes_ShoulderLeftXRayAPWInternalRotationAndWExternalRotation,
  ShoulderBilateralXRayAPWInternalRotationAndWExternalRotation: C80DocTypecodes_ShoulderBilateralXRayAPWInternalRotationAndWExternalRotation,
  AnkleBilateralXRayAPAndLateralStanding: C80DocTypecodes_AnkleBilateralXRayAPAndLateralStanding,
  FootBilateralXRayAPAndLateralStanding: C80DocTypecodes_FootBilateralXRayAPAndLateralStanding,
  FootLeftXRayAPAndLateralStanding: C80DocTypecodes_FootLeftXRayAPAndLateralStanding,
  SpineLumbarXRayAPAndLateralStanding: C80DocTypecodes_SpineLumbarXRayAPAndLateralStanding,
  FootLeftXRayAPAndLateralAndObliqueStanding: C80DocTypecodes_FootLeftXRayAPAndLateralAndObliqueStanding,
  ShoulderLeftXRayAPWInternalRotationAndWExternalRotationAndAxillary: C80DocTypecodes_ShoulderLeftXRayAPWInternalRotationAndWExternalRotationAndAxillary,
  ShoulderBilateralXRayAPWInternalRotationAndWExternalRotationAndAxillary: C80DocTypecodes_ShoulderBilateralXRayAPWInternalRotationAndWExternalRotationAndAxillary,
  ShoulderBilateralXRayAPWInternalRotationAndWExternalRotationAndAxillaryAndOutlet: C80DocTypecodes_ShoulderBilateralXRayAPWInternalRotationAndWExternalRotationAndAxillaryAndOutlet,
  ShoulderLeftXRayAPWInternalRotationAndWExternalRotationAndAxillaryAndY: C80DocTypecodes_ShoulderLeftXRayAPWInternalRotationAndWExternalRotationAndAxillaryAndY,
  ShoulderBilateralXRayAPAndAxillaryAndOutletAnd30DegreeCaudalAngle: C80DocTypecodes_ShoulderBilateralXRayAPAndAxillaryAndOutletAnd30DegreeCaudalAngle,
  SpineLumbarXRayLateralStandingAndWFlexionAndWExtension: C80DocTypecodes_SpineLumbarXRayLateralStandingAndWFlexionAndWExtension,
  ChestXRayLateralAndPAWInspirationAndExpiration: C80DocTypecodes_ChestXRayLateralAndPAWInspirationAndExpiration,
  ShoulderBilateralXRayAPWInternalRotationAndWExternalRotationAndY: C80DocTypecodes_ShoulderBilateralXRayAPWInternalRotationAndWExternalRotationAndY,
  ShoulderBilateralXRayAPWInternalRotationAndWExternalRotationAndAxillaryAndY: C80DocTypecodes_ShoulderBilateralXRayAPWInternalRotationAndWExternalRotationAndAxillaryAndY,
  KneeLeftXRaySunriseAndTunnelStanding: C80DocTypecodes_KneeLeftXRaySunriseAndTunnelStanding,
  ShoulderBilateralXRayAPWInternalRotationAndWestPoint: C80DocTypecodes_ShoulderBilateralXRayAPWInternalRotationAndWestPoint,
  ShoulderLeftXRayAPWInternalRotationAndWestPoint: C80DocTypecodes_ShoulderLeftXRayAPWInternalRotationAndWestPoint,
  ShoulderLeftXRayAPWInternalRotationAndWExternalRotationAndY: C80DocTypecodes_ShoulderLeftXRayAPWInternalRotationAndWExternalRotationAndY,
  KidneyBilateralFluoroscopyWContrastRetrograde: C80DocTypecodes_KidneyBilateralFluoroscopyWContrastRetrograde,
  ShoulderBilateralXRayGrasheyAndOutletAndSerendipity: C80DocTypecodes_ShoulderBilateralXRayGrasheyAndOutletAndSerendipity,
  RibsUpperAnteriorAndPosteriorLeftXRay: C80DocTypecodes_RibsUpperAnteriorAndPosteriorLeftXRay,
  RibsPosteriorBilateralXRay: C80DocTypecodes_RibsPosteriorBilateralXRay,
  RibsUpperPosteriorLeftXRay: C80DocTypecodes_RibsUpperPosteriorLeftXRay,
  KidneyBilateralXRayTomographWOContrast: C80DocTypecodes_KidneyBilateralXRayTomographWOContrast,
  PelvisXRayAndInletAndOutlet: C80DocTypecodes_PelvisXRayAndInletAndOutlet,
  FluoroscopyGuidanceForAbscessDrainageOfLiver: C80DocTypecodes_FluoroscopyGuidanceForAbscessDrainageOfLiver,
  FluoroscopyGuidanceForPlacementOfTubeInChest: C80DocTypecodes_FluoroscopyGuidanceForPlacementOfTubeInChest,
  FistulaFluoroscopyWContrastRetrograde: C80DocTypecodes_FistulaFluoroscopyWContrastRetrograde,
  WristRightXRay3ViewsAndRadialDeviation: C80DocTypecodes_WristRightXRay3ViewsAndRadialDeviation,
  WristRightXRay3ViewsAndUlnarDeviation: C80DocTypecodes_WristRightXRay3ViewsAndUlnarDeviation,
  ScapulaXRayLateralAndOutlet: C80DocTypecodes_ScapulaXRayLateralAndOutlet,
  SpineThoracicAndLumbarXRayScoliosisAPAndLateralStanding: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPAndLateralStanding,
  AnkleRightXRayAPAndLateralStanding: C80DocTypecodes_AnkleRightXRayAPAndLateralStanding,
  AnkleRightXRayAPAndLateralAndObliqueWManualStress: C80DocTypecodes_AnkleRightXRayAPAndLateralAndObliqueWManualStress,
  AnkleRightXRayAndViewWManualStress: C80DocTypecodes_AnkleRightXRayAndViewWManualStress,
  AnkleRightXRayAPAndLateralAndObliqueStanding: C80DocTypecodes_AnkleRightXRayAPAndLateralAndObliqueStanding,
  AnkleRightXRayAndMortise: C80DocTypecodes_AnkleRightXRayAndMortise,
  ElbowRightXRayAndOblique: C80DocTypecodes_ElbowRightXRayAndOblique,
  FootRightXRayAPAndLateralStanding: C80DocTypecodes_FootRightXRayAPAndLateralStanding,
  FootRightXRayAPAndLateralAndObliqueStanding: C80DocTypecodes_FootRightXRayAPAndLateralAndObliqueStanding,
  RadiusRightAndUlnaRightXRayAndOblique: C80DocTypecodes_RadiusRightAndUlnaRightXRayAndOblique,
  HipRightXRayAndLateralCrosstable: C80DocTypecodes_HipRightXRayAndLateralCrosstable,
  KneeRightXRay2ViewsAndOblique: C80DocTypecodes_KneeRightXRay2ViewsAndOblique,
  KneeRightXRay2ViewsAndSunrise: C80DocTypecodes_KneeRightXRay2ViewsAndSunrise,
  KneeRightXRay2ViewsAndSunriseAndTunnel: C80DocTypecodes_KneeRightXRay2ViewsAndSunriseAndTunnel,
  KneeRightXRay2ViewsAndTunnel: C80DocTypecodes_KneeRightXRay2ViewsAndTunnel,
  KneeRightXRay2ViewsAndTunnelStanding: C80DocTypecodes_KneeRightXRay2ViewsAndTunnelStanding,
  KneeRightXRay3ViewsAndSunrise: C80DocTypecodes_KneeRightXRay3ViewsAndSunrise,
  KneeRightXRay4ViewsAndAPStanding: C80DocTypecodes_KneeRightXRay4ViewsAndAPStanding,
  KneeRightXRay4ViewsAndOblique: C80DocTypecodes_KneeRightXRay4ViewsAndOblique,
  KneeRightXRay4ViewsAndTunnel: C80DocTypecodes_KneeRightXRay4ViewsAndTunnel,
  KneeRightXRay4ViewsAndSunriseAndTunnel: C80DocTypecodes_KneeRightXRay4ViewsAndSunriseAndTunnel,
  KneeRightXRayAPAndLateralAndRightObliqueAndLeftOblique: C80DocTypecodes_KneeRightXRayAPAndLateralAndRightObliqueAndLeftOblique,
  KneeRightXRayAndTunnel: C80DocTypecodes_KneeRightXRayAndTunnel,
  KneeRightXRayAndOblique: C80DocTypecodes_KneeRightXRayAndOblique,
  KneeRightXRayAndSunrise: C80DocTypecodes_KneeRightXRayAndSunrise,
  ShoulderRightXRayWInternalRotationAndWExternalRotationAndAxillary: C80DocTypecodes_ShoulderRightXRayWInternalRotationAndWExternalRotationAndAxillary,
  ShoulderRightXRay3ViewsAndAxillary: C80DocTypecodes_ShoulderRightXRay3ViewsAndAxillary,
  ShoulderRightXRay3ViewsAndY: C80DocTypecodes_ShoulderRightXRay3ViewsAndY,
  ShoulderRightXRayAPWInternalRotationAndWExternalRotation: C80DocTypecodes_ShoulderRightXRayAPWInternalRotationAndWExternalRotation,
  ShoulderRightXRayAPWInternalRotationAndWestPoint: C80DocTypecodes_ShoulderRightXRayAPWInternalRotationAndWestPoint,
  ShoulderRightXRayAPWInternalRotationAndWExternalRotationAndWestPoint: C80DocTypecodes_ShoulderRightXRayAPWInternalRotationAndWExternalRotationAndWestPoint,
  TibiaRightAndFibulaRightXRayAndOblique: C80DocTypecodes_TibiaRightAndFibulaRightXRayAndOblique,
  WristRightXRay3ViewsAndCarpalTunnel: C80DocTypecodes_WristRightXRay3ViewsAndCarpalTunnel,
  WristRightXRayAndCarpalTunnel: C80DocTypecodes_WristRightXRayAndCarpalTunnel,
  ShoulderXRayAPAndGrasheyAndAxillary: C80DocTypecodes_ShoulderXRayAPAndGrasheyAndAxillary,
  ShoulderXRayAPWInternalRotationAndWExternalRotation: C80DocTypecodes_ShoulderXRayAPWInternalRotationAndWExternalRotation,
  ShoulderXRayAxillaryAndTranscapular: C80DocTypecodes_ShoulderXRayAxillaryAndTranscapular,
  SinusesXRay3ViewsAndSubmentovertex: C80DocTypecodes_SinusesXRay3ViewsAndSubmentovertex,
  SternumXRayLateralAndRightObliqueAndLeftOblique: C80DocTypecodes_SternumXRayLateralAndRightObliqueAndLeftOblique,
  SternumXRayLateralAndRightAnteriorOblique: C80DocTypecodes_SternumXRayLateralAndRightAnteriorOblique,
  SpineThoracicXRay5ViewsAndOblique: C80DocTypecodes_SpineThoracicXRay5ViewsAndOblique,
  SpineThoracicXRayTomographAP: C80DocTypecodes_SpineThoracicXRayTomographAP,
  SpineThoracicXRayTomographLateral: C80DocTypecodes_SpineThoracicXRayTomographLateral,
  SpineThoracicXRayAPSingleViewWLeftBending: C80DocTypecodes_SpineThoracicXRayAPSingleViewWLeftBending,
  SpineThoracicXRayAPSingleViewWRightBending: C80DocTypecodes_SpineThoracicXRayAPSingleViewWRightBending,
  SpineThoracicXRayAndSwimmers: C80DocTypecodes_SpineThoracicXRayAndSwimmers,
  SpineThoracicXRay4ViewsAndOblique: C80DocTypecodes_SpineThoracicXRay4ViewsAndOblique,
  SpineThoracicXRayAndOblique: C80DocTypecodes_SpineThoracicXRayAndOblique,
  GastrointestineUS: C80DocTypecodes_GastrointestineUS,
  GenitourinarySystemUS: C80DocTypecodes_GenitourinarySystemUS,
  ExtremityVeinBilateralUSDoppler: C80DocTypecodes_ExtremityVeinBilateralUSDoppler,
  RenalVesselsBilateralUSDoppler: C80DocTypecodes_RenalVesselsBilateralUSDoppler,
  LowerExtremityVeinBilateralUSDoppler: C80DocTypecodes_LowerExtremityVeinBilateralUSDoppler,
  LowerExtremityArteryBilateralUSDoppler: C80DocTypecodes_LowerExtremityArteryBilateralUSDoppler,
  LowerExtremityVesselsBilateralUSDoppler: C80DocTypecodes_LowerExtremityVesselsBilateralUSDoppler,
  UpperExtremityArteryBilateralUSDoppler: C80DocTypecodes_UpperExtremityArteryBilateralUSDoppler,
  ExtremityVesselsUSDopplerLimited: C80DocTypecodes_ExtremityVesselsUSDopplerLimited,
  IliacArteryUSDoppler: C80DocTypecodes_IliacArteryUSDoppler,
  RenalVesselsUSDoppler: C80DocTypecodes_RenalVesselsUSDoppler,
  CarotidArteryLeftUSDoppler: C80DocTypecodes_CarotidArteryLeftUSDoppler,
  ExtremityArteryLeftUSDoppler: C80DocTypecodes_ExtremityArteryLeftUSDoppler,
  ExtremityVeinLeftUSDoppler: C80DocTypecodes_ExtremityVeinLeftUSDoppler,
  LowerExtremityVesselsLeftUSDopplerLimited: C80DocTypecodes_LowerExtremityVesselsLeftUSDopplerLimited,
  LowerExtremityVesselsLeftUSDoppler: C80DocTypecodes_LowerExtremityVesselsLeftUSDoppler,
  LowerExtremityVeinLeftUSDoppler: C80DocTypecodes_LowerExtremityVeinLeftUSDoppler,
  UpperExtremityVesselsLeftUSDoppler: C80DocTypecodes_UpperExtremityVesselsLeftUSDoppler,
  LowerExtremityArteryUSDoppler: C80DocTypecodes_LowerExtremityArteryUSDoppler,
  RenalArteryUSDoppler: C80DocTypecodes_RenalArteryUSDoppler,
  RenalVesselsUSDopplerLimited: C80DocTypecodes_RenalVesselsUSDopplerLimited,
  CarotidArteryRightUSDoppler: C80DocTypecodes_CarotidArteryRightUSDoppler,
  ExtremityArteryRightUSDoppler: C80DocTypecodes_ExtremityArteryRightUSDoppler,
  ExtremityVeinRightUSDoppler: C80DocTypecodes_ExtremityVeinRightUSDoppler,
  LowerExtremityVesselsRightUSDopplerLimited: C80DocTypecodes_LowerExtremityVesselsRightUSDopplerLimited,
  LowerExtremityVesselsRightUSDoppler: C80DocTypecodes_LowerExtremityVesselsRightUSDoppler,
  LowerExtremityVeinRightUSDoppler: C80DocTypecodes_LowerExtremityVeinRightUSDoppler,
  UpperExtremityVesselsRightUSDoppler: C80DocTypecodes_UpperExtremityVesselsRightUSDoppler,
  VesselsUSDoppler: C80DocTypecodes_VesselsUSDoppler,
  TesticleVesselsUSDoppler: C80DocTypecodes_TesticleVesselsUSDoppler,
  UpperExtremityArteryUSDoppler: C80DocTypecodes_UpperExtremityArteryUSDoppler,
  UpperExtremityVesselsUSDoppler: C80DocTypecodes_UpperExtremityVesselsUSDoppler,
  ExtremityVeinUSDoppler: C80DocTypecodes_ExtremityVeinUSDoppler,
  GastrointestineUSWContrastPO: C80DocTypecodes_GastrointestineUSWContrastPO,
  USGuidanceForAbscessDrainageOfUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForAbscessDrainageOfUnspecifiedBodyRegion,
  USGuidanceForAspirationOfOvary: C80DocTypecodes_USGuidanceForAspirationOfOvary,
  TendonUS: C80DocTypecodes_TendonUS,
  LiverTransplantUS: C80DocTypecodes_LiverTransplantUS,
  RibsLowerPosteriorXRay: C80DocTypecodes_RibsLowerPosteriorXRay,
  FemurRightAndTibiaRightXRayForLegLength: C80DocTypecodes_FemurRightAndTibiaRightXRayForLegLength,
  RibsUpperAnteriorAndPosteriorRightXRay: C80DocTypecodes_RibsUpperAnteriorAndPosteriorRightXRay,
  RibsUpperPosteriorRightXRay: C80DocTypecodes_RibsUpperPosteriorRightXRay,
  RibsLowerPosteriorRightXRay: C80DocTypecodes_RibsLowerPosteriorRightXRay,
  AbdominalWallUS: C80DocTypecodes_AbdominalWallUS,
  ExtremityVesselsBilateralUSDoppler: C80DocTypecodes_ExtremityVesselsBilateralUSDoppler,
  UpperExtremityVeinBilateralUSDoppler: C80DocTypecodes_UpperExtremityVeinBilateralUSDoppler,
  IliacVesselsUSDoppler: C80DocTypecodes_IliacVesselsUSDoppler,
  FemoralVesselsLeftUSDoppler: C80DocTypecodes_FemoralVesselsLeftUSDoppler,
  LowerExtremityArteryLeftUSDoppler: C80DocTypecodes_LowerExtremityArteryLeftUSDoppler,
  UpperExtremityArteryLeftUSDoppler: C80DocTypecodes_UpperExtremityArteryLeftUSDoppler,
  UpperExtremityVeinLeftUSDoppler: C80DocTypecodes_UpperExtremityVeinLeftUSDoppler,
  OvarianVesselsUSDoppler: C80DocTypecodes_OvarianVesselsUSDoppler,
  ExtremityVesselsRightUSDoppler: C80DocTypecodes_ExtremityVesselsRightUSDoppler,
  FemoralVesselsRightUSDoppler: C80DocTypecodes_FemoralVesselsRightUSDoppler,
  LowerExtremityArteryRightUSDoppler: C80DocTypecodes_LowerExtremityArteryRightUSDoppler,
  UpperExtremityArteryRightUSDoppler: C80DocTypecodes_UpperExtremityArteryRightUSDoppler,
  UpperExtremityVeinRightUSDoppler: C80DocTypecodes_UpperExtremityVeinRightUSDoppler,
  UmbilicalVesselsUSDoppler: C80DocTypecodes_UmbilicalVesselsUSDoppler,
  PancreasTransplantUS: C80DocTypecodes_PancreasTransplantUS,
  LymphaticsPelvicFluoroscopyWContrastIntraLymphatic: C80DocTypecodes_LymphaticsPelvicFluoroscopyWContrastIntraLymphatic,
  PelvisXRayAndOblique: C80DocTypecodes_PelvisXRayAndOblique,
  HipRightXRayAPAndDaneliusMiller: C80DocTypecodes_HipRightXRayAPAndDaneliusMiller,
  HipRightXRayAndDaneliusMiller: C80DocTypecodes_HipRightXRayAndDaneliusMiller,
  HipRightXRayDaneliusMiller: C80DocTypecodes_HipRightXRayDaneliusMiller,
  WristRightXRayLateralWFlexionAndWExtension: C80DocTypecodes_WristRightXRayLateralWFlexionAndWExtension,
  ShoulderXRayStrykerNotch: C80DocTypecodes_ShoulderXRayStrykerNotch,
  ShoulderXRayStrykerNotchAndWestPoint: C80DocTypecodes_ShoulderXRayStrykerNotchAndWestPoint,
  BonesLongXRaySurveyLimited: C80DocTypecodes_BonesLongXRaySurveyLimited,
  SkullXRayPAAndRightLateralAndLeftLateral: C80DocTypecodes_SkullXRayPAAndRightLateralAndLeftLateral,
  SkullXRayPAAndRightLateralAndLeftLateralAndTowne: C80DocTypecodes_SkullXRayPAAndRightLateralAndLeftLateralAndTowne,
  SkullXRayPAAndRightLateralAndLeftLateralAndCaldwellAndTowne: C80DocTypecodes_SkullXRayPAAndRightLateralAndLeftLateralAndCaldwellAndTowne,
  USGuidanceForBiopsyOfLymphNode: C80DocTypecodes_USGuidanceForBiopsyOfLymphNode,
  ArteryUSDoppler: C80DocTypecodes_ArteryUSDoppler,
  VeinUSDopplerLimited: C80DocTypecodes_VeinUSDopplerLimited,
  VeinUSDoppler: C80DocTypecodes_VeinUSDoppler,
  ExtremityUSLimited: C80DocTypecodes_ExtremityUSLimited,
  UnspecifiedBodyRegionUSOfForeignBody: C80DocTypecodes_UnspecifiedBodyRegionUSOfForeignBody,
  PulmonarySystemScan: C80DocTypecodes_PulmonarySystemScan,
  ScanGuidanceForAbscessLocalizationLimited: C80DocTypecodes_ScanGuidanceForAbscessLocalizationLimited,
  SPECTGuidanceForAbscessLocalization: C80DocTypecodes_SPECTGuidanceForAbscessLocalization,
  SPECTGuidanceForAbscessLocalizationWholeBody: C80DocTypecodes_SPECTGuidanceForAbscessLocalizationWholeBody,
  ScanGuidanceForAbscessLocalizationWholeBody: C80DocTypecodes_ScanGuidanceForAbscessLocalizationWholeBody,
  AdrenalGlandScanWI131NP59IV: C80DocTypecodes_AdrenalGlandScanWI131NP59IV,
  ArteryScanWTc99mDTPAIA: C80DocTypecodes_ArteryScanWTc99mDTPAIA,
  VeinBilateralScan: C80DocTypecodes_VeinBilateralScan,
  BoneScanLimited: C80DocTypecodes_BoneScanLimited,
  MeckelsDiverticulumSPECT: C80DocTypecodes_MeckelsDiverticulumSPECT,
  MeckelsDiverticulumScan: C80DocTypecodes_MeckelsDiverticulumScan,
  BrainScanWTc99mHMPAOIV: C80DocTypecodes_BrainScanWTc99mHMPAOIV,
  BrainSPECTWTc99mHMPAOIV: C80DocTypecodes_BrainSPECTWTc99mHMPAOIV,
  BrainSPECT: C80DocTypecodes_BrainSPECT,
  BrainScanStatic: C80DocTypecodes_BrainScanStatic,
  BrainScanStaticLimited: C80DocTypecodes_BrainScanStaticLimited,
  BrainScanWTl201IV: C80DocTypecodes_BrainScanWTl201IV,
  BrainScanFlow: C80DocTypecodes_BrainScanFlow,
  BrainSPECTFlow: C80DocTypecodes_BrainSPECTFlow,
  BrainSPECTWI123IV: C80DocTypecodes_BrainSPECTWI123IV,
  BrainSPECTWTl201IV: C80DocTypecodes_BrainSPECTWTl201IV,
  BrainSPECTWTc99mDTPAIV: C80DocTypecodes_BrainSPECTWTc99mDTPAIV,
  BrainSPECTWTc99mGlucoheptonateIV: C80DocTypecodes_BrainSPECTWTc99mGlucoheptonateIV,
  BrainScanWTc99mGlucoheptonateIV: C80DocTypecodes_BrainScanWTc99mGlucoheptonateIV,
  BrainVeinsScan: C80DocTypecodes_BrainVeinsScan,
  BreastSPECT: C80DocTypecodes_BreastSPECT,
  BreastScanLimited: C80DocTypecodes_BreastScanLimited,
  BreastScan: C80DocTypecodes_BreastScan,
  HeartSPECTWTc99mTetrofosminIV: C80DocTypecodes_HeartSPECTWTc99mTetrofosminIV,
  HeartSPECTWDipyridamoleAndWRadionuclideIV: C80DocTypecodes_HeartSPECTWDipyridamoleAndWRadionuclideIV,
  HeartSPECT: C80DocTypecodes_HeartSPECT,
  HeartScan: C80DocTypecodes_HeartScan,
  HeartScanWAdenosineAndWTl201IV: C80DocTypecodes_HeartScanWAdenosineAndWTl201IV,
  HeartScanWDobutamineAndWTl201IV: C80DocTypecodes_HeartScanWDobutamineAndWTl201IV,
  HeartScanForInfarct: C80DocTypecodes_HeartScanForInfarct,
  HeartSPECTForInfarctWTc99mPYPIV: C80DocTypecodes_HeartSPECTForInfarctWTc99mPYPIV,
  HeartSPECTForInfarctWTc99mSestamibiIV: C80DocTypecodes_HeartSPECTForInfarctWTc99mSestamibiIV,
  HeartSPECTForInfarct: C80DocTypecodes_HeartSPECTForInfarct,
  HeartScanForInfarctWTc99mPYPIV: C80DocTypecodes_HeartScanForInfarctWTc99mPYPIV,
  HeartSPECTAtRestAndWRadionuclideIV: C80DocTypecodes_HeartSPECTAtRestAndWRadionuclideIV,
  HeartScanAtRestAndWDipyridamoleAndWRadionuclideIV: C80DocTypecodes_HeartScanAtRestAndWDipyridamoleAndWRadionuclideIV,
  HeartScanAtRestAndWDobutamineAndWRadionuclideIV: C80DocTypecodes_HeartScanAtRestAndWDobutamineAndWRadionuclideIV,
  HeartSPECTAtRestAndWStressAndWTc99mSestamibiIV: C80DocTypecodes_HeartSPECTAtRestAndWStressAndWTc99mSestamibiIV,
  HeartScanAtRestAndWStressAndWRadionuclideIV: C80DocTypecodes_HeartScanAtRestAndWStressAndWRadionuclideIV,
  HeartScanForShuntDetectionWTc99mMAAIV: C80DocTypecodes_HeartScanForShuntDetectionWTc99mMAAIV,
  HeartScanForShuntDetection: C80DocTypecodes_HeartScanForShuntDetection,
  HeartScanWStressAndW201ThIV: C80DocTypecodes_HeartScanWStressAndW201ThIV,
  HeartScanWStressAndWRadionuclideIV: C80DocTypecodes_HeartScanWStressAndWRadionuclideIV,
  HeartSPECTWStressAndWRadionuclideIV: C80DocTypecodes_HeartSPECTWStressAndWRadionuclideIV,
  ScanWholeBodyWTc99mArcitumomabIV: C80DocTypecodes_ScanWholeBodyWTc99mArcitumomabIV,
  LacrimalDuctScanWRadionuclideIntraLacrimalDuct: C80DocTypecodes_LacrimalDuctScanWRadionuclideIntraLacrimalDuct,
  RectumScanWRadionuclidePO: C80DocTypecodes_RectumScanWRadionuclidePO,
  EsophagusScanForMotilityWRadionuclidePO: C80DocTypecodes_EsophagusScanForMotilityWRadionuclidePO,
  EsophagusScanForRefluxWRadionuclidePO: C80DocTypecodes_EsophagusScanForRefluxWRadionuclidePO,
  GallbladderScanWTc99mDISIDAIV: C80DocTypecodes_GallbladderScanWTc99mDISIDAIV,
  SPECTForInfectionWGA67IV: C80DocTypecodes_SPECTForInfectionWGA67IV,
  ScanStaticForInfectionWGA67IV: C80DocTypecodes_ScanStaticForInfectionWGA67IV,
  ScanForInfectionWGA67IV: C80DocTypecodes_ScanForInfectionWGA67IV,
  SPECTForTumorWGA67IV: C80DocTypecodes_SPECTForTumorWGA67IV,
  ScanForTumorWGA67IV: C80DocTypecodes_ScanForTumorWGA67IV,
  SPECTWholeBodyWGA67IV: C80DocTypecodes_SPECTWholeBodyWGA67IV,
  SPECTLimitedWGA67IV: C80DocTypecodes_SPECTLimitedWGA67IV,
  SPECTWGA67IV: C80DocTypecodes_SPECTWGA67IV,
  ScanWholeBodyWGA67IV: C80DocTypecodes_ScanWholeBodyWGA67IV,
  SPECTForAbscessWGA67IV: C80DocTypecodes_SPECTForAbscessWGA67IV,
  ScanForAbscessWGA67IV: C80DocTypecodes_ScanForAbscessWGA67IV,
  ScanForLymphomaWGA67IV: C80DocTypecodes_ScanForLymphomaWGA67IV,
  ScanLimitedWGA67IV: C80DocTypecodes_ScanLimitedWGA67IV,
  ScanWGA67IV: C80DocTypecodes_ScanWGA67IV,
  GastrointestineScanWTc99mSCIV: C80DocTypecodes_GastrointestineScanWTc99mSCIV,
  LiverScanWTc99mTaggedRBCIV: C80DocTypecodes_LiverScanWTc99mTaggedRBCIV,
  LiverSPECTWTc99mTaggedRBCIV: C80DocTypecodes_LiverSPECTWTc99mTaggedRBCIV,
  LiverSPECT: C80DocTypecodes_LiverSPECT,
  LiverScan: C80DocTypecodes_LiverScan,
  LiverTransplantScan: C80DocTypecodes_LiverTransplantScan,
  LungScanLimited: C80DocTypecodes_LungScanLimited,
  LungScanWDepreotideAndWRadionuclideIV: C80DocTypecodes_LungScanWDepreotideAndWRadionuclideIV,
  LungScanPerfusion: C80DocTypecodes_LungScanPerfusion,
  ScanWholeBodyWI131MIBGIV: C80DocTypecodes_ScanWholeBodyWI131MIBGIV,
  HeartScanPerfusionAtRestAndWTc99mSestamibiIV: C80DocTypecodes_HeartScanPerfusionAtRestAndWTc99mSestamibiIV,
  HeartSPECTPerfusionWAdenosineAndWRadionuclideIV: C80DocTypecodes_HeartSPECTPerfusionWAdenosineAndWRadionuclideIV,
  HeartScanPerfusionWAdenosineAndWRadionuclideIV: C80DocTypecodes_HeartScanPerfusionWAdenosineAndWRadionuclideIV,
  HeartScanPerfusionWDobutamineAndWTc99mSestamibiIV: C80DocTypecodes_HeartScanPerfusionWDobutamineAndWTc99mSestamibiIV,
  HeartScanPerfusionWDobutamineAndWRadionuclideIV: C80DocTypecodes_HeartScanPerfusionWDobutamineAndWRadionuclideIV,
  HeartScanPerfusionWTc99mSestamibiIV: C80DocTypecodes_HeartScanPerfusionWTc99mSestamibiIV,
  HeartScanPerfusionWDipyridamoleAndWTc99mSestamibiIV: C80DocTypecodes_HeartScanPerfusionWDipyridamoleAndWTc99mSestamibiIV,
  HeartScanPerfusionWDipyridamoleAndWTl201IV: C80DocTypecodes_HeartScanPerfusionWDipyridamoleAndWTl201IV,
  HeartScanPerfusionWDipyridamoleAndWRadionuclideIV: C80DocTypecodes_HeartScanPerfusionWDipyridamoleAndWRadionuclideIV,
  HeartScanPerfusionWDipyridamoleAndWTc99mIV: C80DocTypecodes_HeartScanPerfusionWDipyridamoleAndWTc99mIV,
  HeartSPECTPerfusionWTc99mSestamibiIV: C80DocTypecodes_HeartSPECTPerfusionWTc99mSestamibiIV,
  HeartSPECTPerfusionWTl201IV: C80DocTypecodes_HeartSPECTPerfusionWTl201IV,
  HeartSPECTPerfusion: C80DocTypecodes_HeartSPECTPerfusion,
  HeartScanPerfusionWTl201IVAndTc99mTetrofosminIV: C80DocTypecodes_HeartScanPerfusionWTl201IVAndTc99mTetrofosminIV,
  HeartScanPerfusionWTl201IV: C80DocTypecodes_HeartScanPerfusionWTl201IV,
  HeartScanPerfusionWStressAndWTl201IV: C80DocTypecodes_HeartScanPerfusionWStressAndWTl201IV,
  HeartScanPerfusion: C80DocTypecodes_HeartScanPerfusion,
  HeartSPECTPerfusionAtRestAndWRadionuclideIV: C80DocTypecodes_HeartSPECTPerfusionAtRestAndWRadionuclideIV,
  HeartScanPerfusionAtRestAndWAdenosineAndWRadionuclideIV: C80DocTypecodes_HeartScanPerfusionAtRestAndWAdenosineAndWRadionuclideIV,
  HeartScanPerfusionAtRestAndWDipyridamoleAndWTc99mSestamibiIV: C80DocTypecodes_HeartScanPerfusionAtRestAndWDipyridamoleAndWTc99mSestamibiIV,
  HeartScanPerfusionAtRestAndWDipyridamoleAndWRadionuclideIV: C80DocTypecodes_HeartScanPerfusionAtRestAndWDipyridamoleAndWRadionuclideIV,
  HeartSPECTPerfusionAtRestAndWStressAndWTl201IV: C80DocTypecodes_HeartSPECTPerfusionAtRestAndWStressAndWTl201IV,
  HeartSPECTPerfusionAtRestAndWStressAndWRadionuclideIV: C80DocTypecodes_HeartSPECTPerfusionAtRestAndWStressAndWRadionuclideIV,
  HeartSPECTPerfusionAtRestAndWAdenosineAndWTl201IV: C80DocTypecodes_HeartSPECTPerfusionAtRestAndWAdenosineAndWTl201IV,
  HeartScanPerfusionAtRestAndWStressAndWRadionuclideIV: C80DocTypecodes_HeartScanPerfusionAtRestAndWStressAndWRadionuclideIV,
  HeartScanPerfusionAtRestAndWStressAndWTc99mSestamibiIV: C80DocTypecodes_HeartScanPerfusionAtRestAndWStressAndWTc99mSestamibiIV,
  HeartScanPerfusionAtRestAndWRadionuclideIV: C80DocTypecodes_HeartScanPerfusionAtRestAndWRadionuclideIV,
  HeartSPECTPerfusionAtRestAndWTl201IV: C80DocTypecodes_HeartSPECTPerfusionAtRestAndWTl201IV,
  HeartScanPerfusionWStressAndWRadionuclideIV: C80DocTypecodes_HeartScanPerfusionWStressAndWRadionuclideIV,
  HeartScanPerfusionWAdenosineAndWTc99mSestamibiIV: C80DocTypecodes_HeartScanPerfusionWAdenosineAndWTc99mSestamibiIV,
  HeartScanPerfusionWStressAndWTc99mSestamibiIV: C80DocTypecodes_HeartScanPerfusionWStressAndWTc99mSestamibiIV,
  HeartScanPerfusionWDobutamineAndWTl201IV: C80DocTypecodes_HeartScanPerfusionWDobutamineAndWTl201IV,
  HeartSPECTPerfusionWStressAndWRadionuclideIV: C80DocTypecodes_HeartSPECTPerfusionWStressAndWRadionuclideIV,
  HeartScanPerfusionWAdenosineAndWTl201IV: C80DocTypecodes_HeartScanPerfusionWAdenosineAndWTl201IV,
  HeartSPECTPerfusionWStressAndWTc99mSestamibiIV: C80DocTypecodes_HeartSPECTPerfusionWStressAndWTc99mSestamibiIV,
  NeckScan: C80DocTypecodes_NeckScan,
  AbdomenScanWIn111SatumomabIV: C80DocTypecodes_AbdomenScanWIn111SatumomabIV,
  PancreasScan: C80DocTypecodes_PancreasScan,
  ParathyroidSPECT: C80DocTypecodes_ParathyroidSPECT,
  ParathyroidScanDelayed: C80DocTypecodes_ParathyroidScanDelayed,
  ParathyroidScan: C80DocTypecodes_ParathyroidScan,
  ProstateSPECTWTc99mCapromabPendatideIV: C80DocTypecodes_ProstateSPECTWTc99mCapromabPendatideIV,
  ProstateScanWTc99mCapromabPendatideIV: C80DocTypecodes_ProstateScanWTc99mCapromabPendatideIV,
  KidneyBilateralScanWTc99mDTPAIV: C80DocTypecodes_KidneyBilateralScanWTc99mDTPAIV,
  KidneyBilateralScanWTc99mMertiatideIV: C80DocTypecodes_KidneyBilateralScanWTc99mMertiatideIV,
  SalivaryGlandScan: C80DocTypecodes_SalivaryGlandScan,
  SPECTForTumorWTc99mSestamibiIV: C80DocTypecodes_SPECTForTumorWTc99mSestamibiIV,
  ScanForTumorWholeBodyWTc99mSestamibiIV: C80DocTypecodes_ScanForTumorWholeBodyWTc99mSestamibiIV,
  ScanForTumorWTc99mSestamibiIV: C80DocTypecodes_ScanForTumorWTc99mSestamibiIV,
  SpleenScan: C80DocTypecodes_SpleenScan,
  SpleenScanWRadionuclideTaggedHeatDamagedRBCIV: C80DocTypecodes_SpleenScanWRadionuclideTaggedHeatDamagedRBCIV,
  ScrotumAndTesticleScanWTc99mDTPAIV: C80DocTypecodes_ScrotumAndTesticleScanWTc99mDTPAIV,
  ThyroidScanLimitedWI131IV: C80DocTypecodes_ThyroidScanLimitedWI131IV,
  ThyroidSPECTWI131IV: C80DocTypecodes_ThyroidSPECTWI131IV,
  ThyroidScanWTc99mSestamibiIV: C80DocTypecodes_ThyroidScanWTc99mSestamibiIV,
  ThyroidScanWTc99mIV: C80DocTypecodes_ThyroidScanWTc99mIV,
  ScanGuidanceForLocalizationOfTumorOfBreast: C80DocTypecodes_ScanGuidanceForLocalizationOfTumorOfBreast,
  SPECTGuidanceForLocalizationOfTumorLimited: C80DocTypecodes_SPECTGuidanceForLocalizationOfTumorLimited,
  ScanGuidanceForLocalizationOfTumorLimited: C80DocTypecodes_ScanGuidanceForLocalizationOfTumorLimited,
  ScanGuidanceForLocalizationOfTumorLimitedWTc99mSestamibiIV: C80DocTypecodes_ScanGuidanceForLocalizationOfTumorLimitedWTc99mSestamibiIV,
  SPECTGuidanceForLocalizationOfTumor: C80DocTypecodes_SPECTGuidanceForLocalizationOfTumor,
  ScanGuidanceForLocalizationOfTumor: C80DocTypecodes_ScanGuidanceForLocalizationOfTumor,
  VeinScanWTc99mSCIV: C80DocTypecodes_VeinScanWTc99mSCIV,
  VeinScanWTc99mDTPAIV: C80DocTypecodes_VeinScanWTc99mDTPAIV,
  VeinScanWTc99mHDPIV: C80DocTypecodes_VeinScanWTc99mHDPIV,
  StomachScanForGastricEmptyingLiquidPhaseWRadionuclidePO: C80DocTypecodes_StomachScanForGastricEmptyingLiquidPhaseWRadionuclidePO,
  StomachScanForGastricEmptyingWTc99mSCPO: C80DocTypecodes_StomachScanForGastricEmptyingWTc99mSCPO,
  StomachScanForGastricEmptyingWRadionuclidePO: C80DocTypecodes_StomachScanForGastricEmptyingWRadionuclidePO,
  GastrointestineSPECT: C80DocTypecodes_GastrointestineSPECT,
  SPECTForAbscess: C80DocTypecodes_SPECTForAbscess,
  BoneScanWTc99mHMPAOIV: C80DocTypecodes_BoneScanWTc99mHMPAOIV,
  BoneSPECTLimited: C80DocTypecodes_BoneSPECTLimited,
  BoneScanStaticLimited: C80DocTypecodes_BoneScanStaticLimited,
  BoneScanStatic: C80DocTypecodes_BoneScanStatic,
  BoneSPECTWholeBody: C80DocTypecodes_BoneSPECTWholeBody,
  BoneScanStaticWholeBody: C80DocTypecodes_BoneScanStaticWholeBody,
  BoneScanWholeBody: C80DocTypecodes_BoneScanWholeBody,
  BoneScanDelayed: C80DocTypecodes_BoneScanDelayed,
  BoneScanWSM153IV: C80DocTypecodes_BoneScanWSM153IV,
  BoneMarrowSPECTLimited: C80DocTypecodes_BoneMarrowSPECTLimited,
  BoneMarrowScanLimited: C80DocTypecodes_BoneMarrowScanLimited,
  BoneMarrowSPECT: C80DocTypecodes_BoneMarrowSPECT,
  BoneMarrowScanStatic: C80DocTypecodes_BoneMarrowScanStatic,
  BoneMarrowSPECTWholeBody: C80DocTypecodes_BoneMarrowSPECTWholeBody,
  BoneMarrowScanWholeBody: C80DocTypecodes_BoneMarrowScanWholeBody,
  ScanForEndocrineTumorWholeBodyWI131MIBGIV: C80DocTypecodes_ScanForEndocrineTumorWholeBodyWI131MIBGIV,
  ScanForEndocrineTumorWholeBodyWIn111PentetreotideIV: C80DocTypecodes_ScanForEndocrineTumorWholeBodyWIn111PentetreotideIV,
  ScanForTumorWholeBodyWGA67IV: C80DocTypecodes_ScanForTumorWholeBodyWGA67IV,
  ScanForInfectionWholeBodyWGA67IV: C80DocTypecodes_ScanForInfectionWholeBodyWGA67IV,
  ScanForTumorLimitedWGA67IV: C80DocTypecodes_ScanForTumorLimitedWGA67IV,
  LiverScanStatic: C80DocTypecodes_LiverScanStatic,
  LungScanPerfusionWRadionuclideGaseousInhaled: C80DocTypecodes_LungScanPerfusionWRadionuclideGaseousInhaled,
  LungScanVentilationWTc99mDTPAAerosolInhaled: C80DocTypecodes_LungScanVentilationWTc99mDTPAAerosolInhaled,
  LungScanVentilationWRadionuclideAerosolInhaled: C80DocTypecodes_LungScanVentilationWRadionuclideAerosolInhaled,
  LungScanVentilationWRadionuclideGaseousInhaled: C80DocTypecodes_LungScanVentilationWRadionuclideGaseousInhaled,
  LungScanVentilationWRadionuclideInhaled: C80DocTypecodes_LungScanVentilationWRadionuclideInhaled,
  LungSPECTVentilationAndPerfusionWRadionuclideInhaledAndWRadionuclideIV: C80DocTypecodes_LungSPECTVentilationAndPerfusionWRadionuclideInhaledAndWRadionuclideIV,
  SPECTWI131MIBGIV: C80DocTypecodes_SPECTWI131MIBGIV,
  ScanDelayedWI131MIBGIV: C80DocTypecodes_ScanDelayedWI131MIBGIV,
  ScanWI131MIBGIV: C80DocTypecodes_ScanWI131MIBGIV,
  ScanDelayedWIn111SatumomabIV: C80DocTypecodes_ScanDelayedWIn111SatumomabIV,
  ScanLimitedWIn111SatumomabIV: C80DocTypecodes_ScanLimitedWIn111SatumomabIV,
  SPECTWIn111SatumomabIV: C80DocTypecodes_SPECTWIn111SatumomabIV,
  ScanWholeBodyWIn111SatumomabIV: C80DocTypecodes_ScanWholeBodyWIn111SatumomabIV,
  ScanWIn111SatumomabIV: C80DocTypecodes_ScanWIn111SatumomabIV,
  ParotidGlandScanFlow: C80DocTypecodes_ParotidGlandScanFlow,
  PeritoneovenousShuntScanForPatencyWIn111IT: C80DocTypecodes_PeritoneovenousShuntScanForPatencyWIn111IT,
  PeritoneovenousShuntScanForPatencyWRadionuclideIT: C80DocTypecodes_PeritoneovenousShuntScanForPatencyWRadionuclideIT,
  KidneyBilateralScanWI131IV: C80DocTypecodes_KidneyBilateralScanWI131IV,
  KidneyBilateralSPECTWTc99mMertiatideIV: C80DocTypecodes_KidneyBilateralSPECTWTc99mMertiatideIV,
  KidneyBilateralSPECT: C80DocTypecodes_KidneyBilateralSPECT,
  KidneyBilateralScanStatic: C80DocTypecodes_KidneyBilateralScanStatic,
  KidneyBilateralScanStaticWTc99mDMSAIV: C80DocTypecodes_KidneyBilateralScanStaticWTc99mDMSAIV,
  ScrotumAndTesticleScanStatic: C80DocTypecodes_ScrotumAndTesticleScanStatic,
  ThyroidScanFlow: C80DocTypecodes_ThyroidScanFlow,
  AdrenalGlandScanWI131MIBGIV: C80DocTypecodes_AdrenalGlandScanWI131MIBGIV,
  BoneScanFlow: C80DocTypecodes_BoneScanFlow,
  BrainScanDelayedStatic: C80DocTypecodes_BrainScanDelayedStatic,
  HeartScanBloodPoolWStressAndWRadionuclideIV: C80DocTypecodes_HeartScanBloodPoolWStressAndWRadionuclideIV,
  HeartScanBloodPool: C80DocTypecodes_HeartScanBloodPool,
  HeartSPECTBloodPoolAtRestAndWRadionuclideIV: C80DocTypecodes_HeartSPECTBloodPoolAtRestAndWRadionuclideIV,
  HeartScanFirstPassAtRestAndWStressAndWRadionuclideIV: C80DocTypecodes_HeartScanFirstPassAtRestAndWStressAndWRadionuclideIV,
  HeartScanFirstPass: C80DocTypecodes_HeartScanFirstPass,
  LeftVentricleScanFirstPass: C80DocTypecodes_LeftVentricleScanFirstPass,
  HeartScanFirstPassAtRestAndWTc99mSestamibiIV: C80DocTypecodes_HeartScanFirstPassAtRestAndWTc99mSestamibiIV,
  HeartScanFirstPassAtRestAndWRadionuclideIV: C80DocTypecodes_HeartScanFirstPassAtRestAndWRadionuclideIV,
  HeartScanFirstPassWStressAndWTc99mSestamibiIV: C80DocTypecodes_HeartScanFirstPassWStressAndWTc99mSestamibiIV,
  HeartScanFirstPassWStressAndWRadionuclideIV: C80DocTypecodes_HeartScanFirstPassWStressAndWRadionuclideIV,
  HeartScanFlowWTc99mPertechnetateIV: C80DocTypecodes_HeartScanFlowWTc99mPertechnetateIV,
  HeartScanFlow: C80DocTypecodes_HeartScanFlow,
  HeartSPECTWallMotion: C80DocTypecodes_HeartSPECTWallMotion,
  HeartScanWallMotion: C80DocTypecodes_HeartScanWallMotion,
  HeadCisternScanDelayedWRadionuclideIT: C80DocTypecodes_HeadCisternScanDelayedWRadionuclideIT,
  ScanDelayedWGA67IV: C80DocTypecodes_ScanDelayedWGA67IV,
  LiverAndSpleenSPECT: C80DocTypecodes_LiverAndSpleenSPECT,
  LiverAndSpleenScan: C80DocTypecodes_LiverAndSpleenScan,
  LiverAndSpleenScanStatic: C80DocTypecodes_LiverAndSpleenScanStatic,
  BoneSPECT1Phase: C80DocTypecodes_BoneSPECT1Phase,
  BoneScan2ViewsPhase: C80DocTypecodes_BoneScan2ViewsPhase,
  BoneSPECT3PhaseWholeBody: C80DocTypecodes_BoneSPECT3PhaseWholeBody,
  BoneScan3ViewsPhaseWholeBody: C80DocTypecodes_BoneScan3ViewsPhaseWholeBody,
  BoneScan3ViewsPhase: C80DocTypecodes_BoneScan3ViewsPhase,
  BoneScanBloodPool: C80DocTypecodes_BoneScanBloodPool,
  HeartScanFirstPassAndVentricularVolume: C80DocTypecodes_HeartScanFirstPassAndVentricularVolume,
  HeartScanFirstPassAndWallMotionAtRestAndWRadionuclideIV: C80DocTypecodes_HeartScanFirstPassAndWallMotionAtRestAndWRadionuclideIV,
  HeartScanFirstPassAndEjectionFractionAtRestAndWRadionuclideIV: C80DocTypecodes_HeartScanFirstPassAndEjectionFractionAtRestAndWRadionuclideIV,
  HeartScanFirstPassAndWallMotionWStressAndWRadionuclideIV: C80DocTypecodes_HeartScanFirstPassAndWallMotionWStressAndWRadionuclideIV,
  HeartScanFirstPassAndEjectionFraction: C80DocTypecodes_HeartScanFirstPassAndEjectionFraction,
  HeartScanFirstPassAndWallMotion: C80DocTypecodes_HeartScanFirstPassAndWallMotion,
  HeartScanForInfarctAndFirstPassWTc99mPYPIV: C80DocTypecodes_HeartScanForInfarctAndFirstPassWTc99mPYPIV,
  HeartScanForInfarctAndFirstPass: C80DocTypecodes_HeartScanForInfarctAndFirstPass,
  HeartScanFlowForShuntDetection: C80DocTypecodes_HeartScanFlowForShuntDetection,
  HeartScanStaticForShuntDetection: C80DocTypecodes_HeartScanStaticForShuntDetection,
  GallbladderScanEjectionFractionWTc99mDISIDAIV: C80DocTypecodes_GallbladderScanEjectionFractionWTc99mDISIDAIV,
  ScanStaticForTumorWGA67IV: C80DocTypecodes_ScanStaticForTumorWGA67IV,
  LiverAndLungScan: C80DocTypecodes_LiverAndLungScan,
  LungSPECTVentilationWRadionuclideAerosolInhaled: C80DocTypecodes_LungSPECTVentilationWRadionuclideAerosolInhaled,
  SalivaryGlandScanFlow: C80DocTypecodes_SalivaryGlandScanFlow,
  SalivaryGlandScanStatic: C80DocTypecodes_SalivaryGlandScanStatic,
  BoneScan3ViewsPhaseMultipleAreas: C80DocTypecodes_BoneScan3ViewsPhaseMultipleAreas,
  BoneScan3ViewsPhaseSingleArea: C80DocTypecodes_BoneScan3ViewsPhaseSingleArea,
  BoneScanStaticMultipleAreas: C80DocTypecodes_BoneScanStaticMultipleAreas,
  BoneScanMultipleAreas: C80DocTypecodes_BoneScanMultipleAreas,
  BoneSPECTMultipleAreas: C80DocTypecodes_BoneSPECTMultipleAreas,
  BoneMarrowSPECTMultipleAreas: C80DocTypecodes_BoneMarrowSPECTMultipleAreas,
  BoneMarrowScanMultipleAreas: C80DocTypecodes_BoneMarrowScanMultipleAreas,
  HeartScanFirstPassAndWallMotionAndVentricularVolumeWStressAndWRadionuclideIV: C80DocTypecodes_HeartScanFirstPassAndWallMotionAndVentricularVolumeWStressAndWRadionuclideIV,
  HeartScanFirstPassAndWallMotionAndVentricularVolumeAndEjectionFractionWStressAndWRadionuclideIV: C80DocTypecodes_HeartScanFirstPassAndWallMotionAndVentricularVolumeAndEjectionFractionWStressAndWRadionuclideIV,
  HeartScanFirstPassAndWallMotionAndEjectionFraction: C80DocTypecodes_HeartScanFirstPassAndWallMotionAndEjectionFraction,
  HeartScanFirstPassAndWallMotionAndVentricularVolumeAndEjectionFraction: C80DocTypecodes_HeartScanFirstPassAndWallMotionAndVentricularVolumeAndEjectionFraction,
  HeartSPECTGatedAndEjectionFraction: C80DocTypecodes_HeartSPECTGatedAndEjectionFraction,
  HeartScanGatedWTc99mSestamibiIV: C80DocTypecodes_HeartScanGatedWTc99mSestamibiIV,
  HeartScanGated: C80DocTypecodes_HeartScanGated,
  HeartSPECTGated: C80DocTypecodes_HeartSPECTGated,
  HeartScanGatedAndEjectionFraction: C80DocTypecodes_HeartScanGatedAndEjectionFraction,
  HeartSPECTGatedAndWallMotion: C80DocTypecodes_HeartSPECTGatedAndWallMotion,
  HeartScanGatedAndFirstPass: C80DocTypecodes_HeartScanGatedAndFirstPass,
  HeartScanGatedAtRestAndWTc99mSestamibiIV: C80DocTypecodes_HeartScanGatedAtRestAndWTc99mSestamibiIV,
  HeartScanGatedAtRestAndWRadionuclideIV: C80DocTypecodes_HeartScanGatedAtRestAndWRadionuclideIV,
  HeartScanGatedAtRestAndWTc99mPertechnetateIV: C80DocTypecodes_HeartScanGatedAtRestAndWTc99mPertechnetateIV,
  HeartScanGatedAndEjectionFractionAtRestAndWRadionuclideIV: C80DocTypecodes_HeartScanGatedAndEjectionFractionAtRestAndWRadionuclideIV,
  HeartScanGatedAtRestAndWStressAndWRadionuclideIV: C80DocTypecodes_HeartScanGatedAtRestAndWStressAndWRadionuclideIV,
  HeartScanGatedAndWallMotionAndEjectionFractionAtRestAndWRadionuclideIV: C80DocTypecodes_HeartScanGatedAndWallMotionAndEjectionFractionAtRestAndWRadionuclideIV,
  HeartScanGatedWStressAndWTc99mPertechnetateIV: C80DocTypecodes_HeartScanGatedWStressAndWTc99mPertechnetateIV,
  HeartScanGatedWStressAndWRadionuclideIV: C80DocTypecodes_HeartScanGatedWStressAndWRadionuclideIV,
  HeartScanGatedAndWallMotionWStressAndWRadionuclideIV: C80DocTypecodes_HeartScanGatedAndWallMotionWStressAndWRadionuclideIV,
  HeartSPECTGatedWStressAndWRadionuclideIV: C80DocTypecodes_HeartSPECTGatedWStressAndWRadionuclideIV,
  HeartScanGatedAndWallMotionAndEjectionFraction: C80DocTypecodes_HeartScanGatedAndWallMotionAndEjectionFraction,
  HeartScanWallMotionAndEjectionFraction: C80DocTypecodes_HeartScanWallMotionAndEjectionFraction,
  ScanForInfectionMultipleAreasWGA67IV: C80DocTypecodes_ScanForInfectionMultipleAreasWGA67IV,
  ScanForTumorMultipleAreasWGA67IV: C80DocTypecodes_ScanForTumorMultipleAreasWGA67IV,
  ScanMultipleAreasWGA67IV: C80DocTypecodes_ScanMultipleAreasWGA67IV,
  JointScanLimited: C80DocTypecodes_JointScanLimited,
  JointScanMultipleAreas: C80DocTypecodes_JointScanMultipleAreas,
  JointSPECT: C80DocTypecodes_JointSPECT,
  JointScan: C80DocTypecodes_JointScan,
  LungScanClearanceWTc99mDTPAAerosolInhaled: C80DocTypecodes_LungScanClearanceWTc99mDTPAAerosolInhaled,
  LungScanPerfusionWParticulateRadionuclideIV: C80DocTypecodes_LungScanPerfusionWParticulateRadionuclideIV,
  LungScanVentilationAndPerfusionWRadionuclideInhaledSingleBreathAndWParticulateRadionuclideIV: C80DocTypecodes_LungScanVentilationAndPerfusionWRadionuclideInhaledSingleBreathAndWParticulateRadionuclideIV,
  LungScanVentilationAndPerfusionWRadionuclideInhaledAndWParticulateRadionuclideIV: C80DocTypecodes_LungScanVentilationAndPerfusionWRadionuclideInhaledAndWParticulateRadionuclideIV,
  LungScanVentilationAndEquilibriumAndWashoutWRadionuclideInhaled: C80DocTypecodes_LungScanVentilationAndEquilibriumAndWashoutWRadionuclideInhaled,
  LungScanVentilationWRadionuclideGaseousInhaledSingleBreath: C80DocTypecodes_LungScanVentilationWRadionuclideGaseousInhaledSingleBreath,
  LungScanVentilationAndPerfusionAndDifferentialWRadionuclideInhaledAndWRadionuclideIV: C80DocTypecodes_LungScanVentilationAndPerfusionAndDifferentialWRadionuclideInhaledAndWRadionuclideIV,
  LungScanVentilationAndEquilibriumWRadionuclideInhaledSingleBreath: C80DocTypecodes_LungScanVentilationAndEquilibriumWRadionuclideInhaledSingleBreath,
  LungScanVentilationAndEquilibriumAndWashoutWRadionuclideInhaledSingleBreath: C80DocTypecodes_LungScanVentilationAndEquilibriumAndWashoutWRadionuclideInhaledSingleBreath,
  ScanMultipleAreasWIn111SatumomabIV: C80DocTypecodes_ScanMultipleAreasWIn111SatumomabIV,
  ProstateScanMultipleAreasWTc99mCapromabPendatideIV: C80DocTypecodes_ProstateScanMultipleAreasWTc99mCapromabPendatideIV,
  ScanForTumorMultipleAreaWTc99mSestamibiIV: C80DocTypecodes_ScanForTumorMultipleAreaWTc99mSestamibiIV,
  ScrotumAndTesticleScanStaticAndFlow: C80DocTypecodes_ScrotumAndTesticleScanStaticAndFlow,
  ScanGuidanceForLocalizationOfTumorMultipleAreas: C80DocTypecodes_ScanGuidanceForLocalizationOfTumorMultipleAreas,
  VeinScanForThrombosis: C80DocTypecodes_VeinScanForThrombosis,
  VAL11DeoxycorticosteronePresenceInSerumOrPlasma: C80DocTypecodes_VAL11DeoxycorticosteronePresenceInSerumOrPlasma,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma1HourPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma1HourPostXXXChallenge,
  VAL11DeoxycorticosteroneMolesVolumeIn24HourUrine: C80DocTypecodes_VAL11DeoxycorticosteroneMolesVolumeIn24HourUrine,
  GallbladderScanWCholecystokininAndWRadionuclideIV: C80DocTypecodes_GallbladderScanWCholecystokininAndWRadionuclideIV,
  KidneyBilateralScanWTc99mDMSAIV: C80DocTypecodes_KidneyBilateralScanWTc99mDMSAIV,
  BoneSPECTWIn111TaggedWBCIV: C80DocTypecodes_BoneSPECTWIn111TaggedWBCIV,
  FacialBonesXRayPortable: C80DocTypecodes_FacialBonesXRayPortable,
  NeckXRayLateralPortable: C80DocTypecodes_NeckXRayLateralPortable,
  PelvisXRaySingleViewPortable: C80DocTypecodes_PelvisXRaySingleViewPortable,
  PelvisAndHipRightXRayAPAndLateralFrogPortable: C80DocTypecodes_PelvisAndHipRightXRayAPAndLateralFrogPortable,
  HipRightXRayAPAndLateralPortable: C80DocTypecodes_HipRightXRayAPAndLateralPortable,
  FemurRightXRayPortable: C80DocTypecodes_FemurRightXRayPortable,
  KneeRightXRayPortable: C80DocTypecodes_KneeRightXRayPortable,
  AnkleRightXRayPortable: C80DocTypecodes_AnkleRightXRayPortable,
  ShoulderRightXRayPortable: C80DocTypecodes_ShoulderRightXRayPortable,
  HumerusRightXRayPortable: C80DocTypecodes_HumerusRightXRayPortable,
  ElbowRightXRayLimited: C80DocTypecodes_ElbowRightXRayLimited,
  ElbowRightXRayPortable: C80DocTypecodes_ElbowRightXRayPortable,
  WristRightXRayPortable: C80DocTypecodes_WristRightXRayPortable,
  HandRightXRayPortable: C80DocTypecodes_HandRightXRayPortable,
  HandRightXRayLimited: C80DocTypecodes_HandRightXRayLimited,
  ChestXRayDuringSurgery: C80DocTypecodes_ChestXRayDuringSurgery,
  RibsRightXRayPortable: C80DocTypecodes_RibsRightXRayPortable,
  ChestXRayRightObliqueAndLeftOblique: C80DocTypecodes_ChestXRayRightObliqueAndLeftOblique,
  AbdomenXRayDuringSurgery: C80DocTypecodes_AbdomenXRayDuringSurgery,
  UpperGastrointestineAndSmallBowelFluoroscopyWAirContrastPO: C80DocTypecodes_UpperGastrointestineAndSmallBowelFluoroscopyWAirContrastPO,
  ColonFluoroscopyLimitedWAirAndBariumContrastPR: C80DocTypecodes_ColonFluoroscopyLimitedWAirAndBariumContrastPR,
  USGuidanceForDrainageOfProstate: C80DocTypecodes_USGuidanceForDrainageOfProstate,
  FluoroscopyGuidanceForPlacementOfTubeInLiver: C80DocTypecodes_FluoroscopyGuidanceForPlacementOfTubeInLiver,
  FluoroscopyGuidanceForDrainageOfPharynx: C80DocTypecodes_FluoroscopyGuidanceForDrainageOfPharynx,
  FluoroscopicAngiogramGuidanceForPlacementOfCatheterInPortalVeinWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfCatheterInPortalVeinWContrastIV,
  FluoroscopyGuidanceForBiopsyOfProstate: C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfProstate,
  FluoroscopyGuidanceForNeedleBiopsyOfBreast: C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfBreast,
  UnspecifiedBodyRegionCTAnd3DReconstruction: C80DocTypecodes_UnspecifiedBodyRegionCTAnd3DReconstruction,
  AbdomenCT: C80DocTypecodes_AbdomenCT,
  OrbitCT: C80DocTypecodes_OrbitCT,
  FacialBonesAndMaxillaCT: C80DocTypecodes_FacialBonesAndMaxillaCT,
  USGuidanceForDrainageOfAbdomenRetroperitoneum: C80DocTypecodes_USGuidanceForDrainageOfAbdomenRetroperitoneum,
  CTGuidanceForRemovalOfFluidFromAbdomen: C80DocTypecodes_CTGuidanceForRemovalOfFluidFromAbdomen,
  RibsBilateralAndChestXRayAndPAChest: C80DocTypecodes_RibsBilateralAndChestXRayAndPAChest,
  LowerExtremityArteryUSLimited: C80DocTypecodes_LowerExtremityArteryUSLimited,
  UpperExtremityArteryUSLimited: C80DocTypecodes_UpperExtremityArteryUSLimited,
  UpperExtremityArteryRightUS: C80DocTypecodes_UpperExtremityArteryRightUS,
  LowerExtremityArteryRightUS: C80DocTypecodes_LowerExtremityArteryRightUS,
  ExtremityVeinsRightUS: C80DocTypecodes_ExtremityVeinsRightUS,
  HipLeftXRayAPAndLateralPortable: C80DocTypecodes_HipLeftXRayAPAndLateralPortable,
  FemurLeftXRayPortable: C80DocTypecodes_FemurLeftXRayPortable,
  KneeLeftXRay2ViewsAndTunnel: C80DocTypecodes_KneeLeftXRay2ViewsAndTunnel,
  KneeLeftXRayPortable: C80DocTypecodes_KneeLeftXRayPortable,
  AnkleLeftXRayPortable: C80DocTypecodes_AnkleLeftXRayPortable,
  ShoulderLeftXRayPortable: C80DocTypecodes_ShoulderLeftXRayPortable,
  HumerusLeftXRayPortable: C80DocTypecodes_HumerusLeftXRayPortable,
  ElbowLeftXRayLimited: C80DocTypecodes_ElbowLeftXRayLimited,
  ElbowLeftXRayPortable: C80DocTypecodes_ElbowLeftXRayPortable,
  WristLeftXRayPortable: C80DocTypecodes_WristLeftXRayPortable,
  HandLeftXRayPortable: C80DocTypecodes_HandLeftXRayPortable,
  HandLeftXRayLimited: C80DocTypecodes_HandLeftXRayLimited,
  RibsLeftXRayPortable: C80DocTypecodes_RibsLeftXRayPortable,
  RibsLeftAndChestXRayAndPAChest: C80DocTypecodes_RibsLeftAndChestXRayAndPAChest,
  UpperExtremityArteryLeftUS: C80DocTypecodes_UpperExtremityArteryLeftUS,
  LowerExtremityArteryLeftUS: C80DocTypecodes_LowerExtremityArteryLeftUS,
  ExtremityVeinsLeftUS: C80DocTypecodes_ExtremityVeinsLeftUS,
  BoneScanLimitedWIn111TaggedWBCIV: C80DocTypecodes_BoneScanLimitedWIn111TaggedWBCIV,
  SPECTWholeBodyWTc99mArcitumomabIV: C80DocTypecodes_SPECTWholeBodyWTc99mArcitumomabIV,
  ProstateSPECTWIn111SatumomabIV: C80DocTypecodes_ProstateSPECTWIn111SatumomabIV,
  MastoidBilateralXRayLimited: C80DocTypecodes_MastoidBilateralXRayLimited,
  HumerusXRayDuringSurgery: C80DocTypecodes_HumerusXRayDuringSurgery,
  ChestXRay2ViewsAndApical: C80DocTypecodes_ChestXRay2ViewsAndApical,
  RibsRightAndChestXRayAndPAChest: C80DocTypecodes_RibsRightAndChestXRayAndPAChest,
  ChestAndAbdomenXRayAPAndPAChest: C80DocTypecodes_ChestAndAbdomenXRayAPAndPAChest,
  GastrointestineUpperFluoroscopyWWaterSolubleContrastPO: C80DocTypecodes_GastrointestineUpperFluoroscopyWWaterSolubleContrastPO,
  UrinaryBladderAndUrethraFluoroscopyWContrast: C80DocTypecodes_UrinaryBladderAndUrethraFluoroscopyWContrast,
  FluoroscopyGuidanceForReplacementOfPercutaneousCholecystostomyInAbdomen: C80DocTypecodes_FluoroscopyGuidanceForReplacementOfPercutaneousCholecystostomyInAbdomen,
  VeinFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIA: C80DocTypecodes_VeinFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIA,
  AbdomenXRayAPUprightAndLeftLateralDecubitus: C80DocTypecodes_AbdomenXRayAPUprightAndLeftLateralDecubitus,
  CTGuidanceForNeedleLocalizationOfSpineLumbar: C80DocTypecodes_CTGuidanceForNeedleLocalizationOfSpineLumbar,
  CTGuidanceForNeedleLocalizationOfSpineCervical: C80DocTypecodes_CTGuidanceForNeedleLocalizationOfSpineCervical,
  BreastUSScreening: C80DocTypecodes_BreastUSScreening,
  USGuidanceForAbscessDrainageOfLiver: C80DocTypecodes_USGuidanceForAbscessDrainageOfLiver,
  USGuidanceForAspirationOfThyroid: C80DocTypecodes_USGuidanceForAspirationOfThyroid,
  USGuidanceForBiopsyOfSuperficialBone: C80DocTypecodes_USGuidanceForBiopsyOfSuperficialBone,
  CTGuidanceForBiopsyOfHeart: C80DocTypecodes_CTGuidanceForBiopsyOfHeart,
  USGuidanceForBiopsyOfMediastinum: C80DocTypecodes_USGuidanceForBiopsyOfMediastinum,
  USGuidanceForPercutaneousBiopsyOfMuscle: C80DocTypecodes_USGuidanceForPercutaneousBiopsyOfMuscle,
  USGuidanceForPlacementOfTubeInChest: C80DocTypecodes_USGuidanceForPlacementOfTubeInChest,
  USGuidanceForRemovalOfCatheterFromCentralVeinTunneled: C80DocTypecodes_USGuidanceForRemovalOfCatheterFromCentralVeinTunneled,
  UterusAndFallopianTubesUSWSalineIntrauterine: C80DocTypecodes_UterusAndFallopianTubesUSWSalineIntrauterine,
  ExtremityVeinRightUS: C80DocTypecodes_ExtremityVeinRightUS,
  ExtremityVeinLeftUS: C80DocTypecodes_ExtremityVeinLeftUS,
  CarotidArteryUSDoppler: C80DocTypecodes_CarotidArteryUSDoppler,
  IliacGraftUSDoppler: C80DocTypecodes_IliacGraftUSDoppler,
  HeartUS: C80DocTypecodes_HeartUS,
  CarotidArteryLeftUSLimited: C80DocTypecodes_CarotidArteryLeftUSLimited,
  IliacGraftUSDopplerLimited: C80DocTypecodes_IliacGraftUSDopplerLimited,
  CarotidArteryRightUSLimited: C80DocTypecodes_CarotidArteryRightUSLimited,
  PelvisVesselsUSDopplerLimited: C80DocTypecodes_PelvisVesselsUSDopplerLimited,
  ExtremityXRaySingleView: C80DocTypecodes_ExtremityXRaySingleView,
  VesselsFluoroscopicAngiogramWContrastIA: C80DocTypecodes_VesselsFluoroscopicAngiogramWContrastIA,
  ExtremityVesselsFluoroscopicAngiogramWContrastIV: C80DocTypecodes_ExtremityVesselsFluoroscopicAngiogramWContrastIV,
  AdrenalGlandScan: C80DocTypecodes_AdrenalGlandScan,
  SellaTurcicaXRay: C80DocTypecodes_SellaTurcicaXRay,
  ShuntXRay: C80DocTypecodes_ShuntXRay,
  HeartScanWDobutamineAndWRadionuclideIV: C80DocTypecodes_HeartScanWDobutamineAndWRadionuclideIV,
  GastrointestineUpperFluoroscopyAndAPWWaterSolubleContrastPO: C80DocTypecodes_GastrointestineUpperFluoroscopyAndAPWWaterSolubleContrastPO,
  SpineLumbarXRayAndOblique: C80DocTypecodes_SpineLumbarXRayAndOblique,
  SpineCervicalXRayAndOblique: C80DocTypecodes_SpineCervicalXRayAndOblique,
  RibsAndChestXRayAndPAChest: C80DocTypecodes_RibsAndChestXRayAndPAChest,
  HeartScan2ViewsAtRestAndWTl201IV: C80DocTypecodes_HeartScan2ViewsAtRestAndWTl201IV,
  PelvisAndHipBilateralXRayAPAndLateralFrog: C80DocTypecodes_PelvisAndHipBilateralXRayAPAndLateralFrog,
  BreastRightFFDMammogramDiagnostic: C80DocTypecodes_BreastRightFFDMammogramDiagnostic,
  BreastLeftFFDMammogramDiagnostic: C80DocTypecodes_BreastLeftFFDMammogramDiagnostic,
  ScanForLymphoma: C80DocTypecodes_ScanForLymphoma,
  ScanForTumorWholeBody: C80DocTypecodes_ScanForTumorWholeBody,
  BreastBilateralFFDMammogramScreening: C80DocTypecodes_BreastBilateralFFDMammogramScreening,
  ScanWholeBody: C80DocTypecodes_ScanWholeBody,
  FDAPackageInsertDrugAbuseAndDependenceSection: C80DocTypecodes_FDAPackageInsertDrugAbuseAndDependenceSection,
  FDAPackageInsertPregnancySection: C80DocTypecodes_FDAPackageInsertPregnancySection,
  FDAPackageInsertStructuredPatientLabellingUnclassifiedSection: C80DocTypecodes_FDAPackageInsertStructuredPatientLabellingUnclassifiedSection,
  FDAPackageInsertStructuredProductLaballingPatientPackageInsertSection: C80DocTypecodes_FDAPackageInsertStructuredProductLaballingPatientPackageInsertSection,
  FDAPackageInsertStructuredProductLabellingMedguideSection: C80DocTypecodes_FDAPackageInsertStructuredProductLabellingMedguideSection,
  FDAPackageInsertPrecautionsSection: C80DocTypecodes_FDAPackageInsertPrecautionsSection,
  CTGuidanceForBiopsyOfUnspecifiedBodyRegionWContrastIV: C80DocTypecodes_CTGuidanceForBiopsyOfUnspecifiedBodyRegionWContrastIV,
  KidneyBilateralScanFlow: C80DocTypecodes_KidneyBilateralScanFlow,
  LiverScanFlow: C80DocTypecodes_LiverScanFlow,
  SpleenScanFlow: C80DocTypecodes_SpleenScanFlow,
  CTGuidanceForBiopsyOfSuperficialBone: C80DocTypecodes_CTGuidanceForBiopsyOfSuperficialBone,
  CTGuidanceForNeedleBiopsyOfSuperficialBone: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfSuperficialBone,
  CTGuidanceForNeedleBiopsyOfLymphNode: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfLymphNode,
  ExtremityCTWAndWOContrastIV: C80DocTypecodes_ExtremityCTWAndWOContrastIV,
  ChestAndAbdomenXRay: C80DocTypecodes_ChestAndAbdomenXRay,
  SpineCervicalMRIWFlexionAndWExtension: C80DocTypecodes_SpineCervicalMRIWFlexionAndWExtension,
  ThyroidScanAndUptakeWI123IV: C80DocTypecodes_ThyroidScanAndUptakeWI123IV,
  ChestXRayPAAndLateral: C80DocTypecodes_ChestXRayPAAndLateral,
  AnkleBilateralXRay6Views: C80DocTypecodes_AnkleBilateralXRay6Views,
  AbdomenAndPelvisCTWAndWOContrastIV: C80DocTypecodes_AbdomenAndPelvisCTWAndWOContrastIV,
  ChestAndAbdomenCTWContrastIV: C80DocTypecodes_ChestAndAbdomenCTWContrastIV,
  ChestAndAbdomenCTWOContrast: C80DocTypecodes_ChestAndAbdomenCTWOContrast,
  ChestAndAbdomenCTWAndWOContrastIV: C80DocTypecodes_ChestAndAbdomenCTWAndWOContrastIV,
  ExtremityCTWOContrast: C80DocTypecodes_ExtremityCTWOContrast,
  CTGuidanceForBiopsyOfKidney: C80DocTypecodes_CTGuidanceForBiopsyOfKidney,
  CTGuidanceForAbscessDrainageOfAppendix: C80DocTypecodes_CTGuidanceForAbscessDrainageOfAppendix,
  CTGuidanceForAbscessDrainageOfChest: C80DocTypecodes_CTGuidanceForAbscessDrainageOfChest,
  CTGuidanceForAbscessDrainageOfLiver: C80DocTypecodes_CTGuidanceForAbscessDrainageOfLiver,
  CTGuidanceForDrainageOfPancreas: C80DocTypecodes_CTGuidanceForDrainageOfPancreas,
  CTGuidanceForAbscessDrainageOfPleuralSpace: C80DocTypecodes_CTGuidanceForAbscessDrainageOfPleuralSpace,
  CTGuidanceForAbscessDrainageOfKidney: C80DocTypecodes_CTGuidanceForAbscessDrainageOfKidney,
  CTGuidanceForAbscessDrainageOfPelvis: C80DocTypecodes_CTGuidanceForAbscessDrainageOfPelvis,
  CTGuidanceForDrainageOfAbdomenRetroperitoneum: C80DocTypecodes_CTGuidanceForDrainageOfAbdomenRetroperitoneum,
  CTGuidanceForNeedleBiopsyOfAbdomen: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfAbdomen,
  CTGuidanceForNeedleBiopsyOfKidney: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfKidney,
  CTGuidanceForNeedleBiopsyOfPancreas: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfPancreas,
  AbdomenRetroperitoneumCTWOContrast: C80DocTypecodes_AbdomenRetroperitoneumCTWOContrast,
  SPECTForTumorWTl201IV: C80DocTypecodes_SPECTForTumorWTl201IV,
  HeadVesselsCTAngiogramWOContrast: C80DocTypecodes_HeadVesselsCTAngiogramWOContrast,
  PelvisVesselsCTAngiogramWContrastIV: C80DocTypecodes_PelvisVesselsCTAngiogramWContrastIV,
  UpperExtremityVesselsCTAngiogramWContrastIV: C80DocTypecodes_UpperExtremityVesselsCTAngiogramWContrastIV,
  MammogramGuidanceForLocalizationOfBreastLeft: C80DocTypecodes_MammogramGuidanceForLocalizationOfBreastLeft,
  MammogramGuidanceForLocalizationOfBreastRight: C80DocTypecodes_MammogramGuidanceForLocalizationOfBreastRight,
  UnspecifiedBodyRegionMRIWAndWOContrastIV: C80DocTypecodes_UnspecifiedBodyRegionMRIWAndWOContrastIV,
  ClavicleMRIWAndWOContrastIV: C80DocTypecodes_ClavicleMRIWAndWOContrastIV,
  ThyroidMRI: C80DocTypecodes_ThyroidMRI,
  UterusMRI: C80DocTypecodes_UterusMRI,
  ClavicleMRIWOContrast: C80DocTypecodes_ClavicleMRIWOContrast,
  OrbitAndFaceMRI: C80DocTypecodes_OrbitAndFaceMRI,
  HeadVesselsAndNeckVesselsMRIAngiogram: C80DocTypecodes_HeadVesselsAndNeckVesselsMRIAngiogram,
  ScanForTumorWTl201IV: C80DocTypecodes_ScanForTumorWTl201IV,
  HeartScanGatedAndWallMotion: C80DocTypecodes_HeartScanGatedAndWallMotion,
  ScrotumAndTesticleScanFlow: C80DocTypecodes_ScrotumAndTesticleScanFlow,
  HeartScanAtRestAndWStressAndWTl201IV: C80DocTypecodes_HeartScanAtRestAndWStressAndWTl201IV,
  KidneySPECT: C80DocTypecodes_KidneySPECT,
  OrbitLeftXRayForForeignBody: C80DocTypecodes_OrbitLeftXRayForForeignBody,
  OrbitRightXRayForForeignBody: C80DocTypecodes_OrbitRightXRayForForeignBody,
  RibsLeftXRaySingleView: C80DocTypecodes_RibsLeftXRaySingleView,
  RibsRightXRaySingleView: C80DocTypecodes_RibsRightXRaySingleView,
  USGuidanceForBiopsyOfChestPleura: C80DocTypecodes_USGuidanceForBiopsyOfChestPleura,
  FluoroscopyGuidanceForInjectionOfMammaryArteryInternalLeft: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfMammaryArteryInternalLeft,
  SpineCervicalFluoroscopyLimitedWContrastIT: C80DocTypecodes_SpineCervicalFluoroscopyLimitedWContrastIT,
  BrainCTWXe133Inhaled: C80DocTypecodes_BrainCTWXe133Inhaled,
  SpineLumbarXRayAPSingleViewWLeftBending: C80DocTypecodes_SpineLumbarXRayAPSingleViewWLeftBending,
  SpineLumbarXRayAPSingleViewWRightBending: C80DocTypecodes_SpineLumbarXRayAPSingleViewWRightBending,
  AnkleLeftXRayAPAndLateralStanding: C80DocTypecodes_AnkleLeftXRayAPAndLateralStanding,
  RibsLowerPosteriorLeftXRay: C80DocTypecodes_RibsLowerPosteriorLeftXRay,
  AnkleLeftXRayLateralAndMortiseAndBrodenWManualStress: C80DocTypecodes_AnkleLeftXRayLateralAndMortiseAndBrodenWManualStress,
  GallbladderXRayWDoubleDoseContrastPO: C80DocTypecodes_GallbladderXRayWDoubleDoseContrastPO,
  BrainAndPituitaryAndSellaTurcicaMRI: C80DocTypecodes_BrainAndPituitaryAndSellaTurcicaMRI,
  BrainMRICineForCSFFlow: C80DocTypecodes_BrainMRICineForCSFFlow,
  UnspecifiedBodyRegionMRICineForCSFFlow: C80DocTypecodes_UnspecifiedBodyRegionMRICineForCSFFlow,
  ProstateMRIWEndorectalCoil: C80DocTypecodes_ProstateMRIWEndorectalCoil,
  PelvisMRIWEndorectalCoil: C80DocTypecodes_PelvisMRIWEndorectalCoil,
  TransvaginalMRI: C80DocTypecodes_TransvaginalMRI,
  BrainAndPituitaryAndSellaTurcicaMRIWContrastIV: C80DocTypecodes_BrainAndPituitaryAndSellaTurcicaMRIWContrastIV,
  BrainAndPituitaryAndSellaTurcicaMRIWAndWOContrastIV: C80DocTypecodes_BrainAndPituitaryAndSellaTurcicaMRIWAndWOContrastIV,
  BrainAndPituitaryAndSellaTurcicaMRIWOContrast: C80DocTypecodes_BrainAndPituitaryAndSellaTurcicaMRIWOContrast,
  PulmonarySystemCTWXe133Inhaled: C80DocTypecodes_PulmonarySystemCTWXe133Inhaled,
  FootSesamoidBonesBilateralXRayAxial: C80DocTypecodes_FootSesamoidBonesBilateralXRayAxial,
  FootSesamoidBonesLeftXRayAxial: C80DocTypecodes_FootSesamoidBonesLeftXRayAxial,
  ChestXRayFrontalStereo: C80DocTypecodes_ChestXRayFrontalStereo,
  FootXRayObliqueAndAPAndLateralStanding: C80DocTypecodes_FootXRayObliqueAndAPAndLateralStanding,
  FootSesamoidBonesXRay: C80DocTypecodes_FootSesamoidBonesXRay,
  FootSesamoidBonesBilateralXRay: C80DocTypecodes_FootSesamoidBonesBilateralXRay,
  SpineLumbarXRayAPWRBendingAndWLBendingAndWOBendingAndLateral: C80DocTypecodes_SpineLumbarXRayAPWRBendingAndWLBendingAndWOBendingAndLateral,
  UnspecifiedBodyRegionXRayPostMortem: C80DocTypecodes_UnspecifiedBodyRegionXRayPostMortem,
  SpineLumbarXRayAPWRightBendingAndWLeftBending: C80DocTypecodes_SpineLumbarXRayAPWRightBendingAndWLeftBending,
  HipLeftXRayAPAndLateralAndMeasurement: C80DocTypecodes_HipLeftXRayAPAndLateralAndMeasurement,
  KneeXRayAPPowerStandingAndLateralPowerWHyperextension: C80DocTypecodes_KneeXRayAPPowerStandingAndLateralPowerWHyperextension,
  SpineLumbarXRayAPWAndWOLeftBending: C80DocTypecodes_SpineLumbarXRayAPWAndWOLeftBending,
  SpineLumbarXRayAPWAndWORightBending: C80DocTypecodes_SpineLumbarXRayAPWAndWORightBending,
  SpineLumbarXRayAPWRightBendingAndWLeftBendingAndWOBending: C80DocTypecodes_SpineLumbarXRayAPWRightBendingAndWLeftBendingAndWOBending,
  FootSesamoidBonesXRayAPAndLateral: C80DocTypecodes_FootSesamoidBonesXRayAPAndLateral,
  SpineLumbarXRayAPAndLateralAndObliqueAndSpotStanding: C80DocTypecodes_SpineLumbarXRayAPAndLateralAndObliqueAndSpotStanding,
  SpineLumbarXRayAPPowerWRBendingAndWLBendingAndLateralPowerWFlexionAndWExtension: C80DocTypecodes_SpineLumbarXRayAPPowerWRBendingAndWLBendingAndLateralPowerWFlexionAndWExtension,
  ShoulderLeftXRay90DegreeAbduction: C80DocTypecodes_ShoulderLeftXRay90DegreeAbduction,
  SpineLumbarXRayWRightBendingAndWLeftBending: C80DocTypecodes_SpineLumbarXRayWRightBendingAndWLeftBending,
  ChestXRayRightObliqueAndLeftObliqueWNippleMarkers: C80DocTypecodes_ChestXRayRightObliqueAndLeftObliqueWNippleMarkers,
  BreastBilateralMammogramPostWirePlacement: C80DocTypecodes_BreastBilateralMammogramPostWirePlacement,
  BreastLeftMammogramPostWirePlacement: C80DocTypecodes_BreastLeftMammogramPostWirePlacement,
  AnkleBilateralXRayAPAndLateralAndObliqueWManualStress: C80DocTypecodes_AnkleBilateralXRayAPAndLateralAndObliqueWManualStress,
  AnkleLeftXRayAPAndLateralAndObliqueWManualStress: C80DocTypecodes_AnkleLeftXRayAPAndLateralAndObliqueWManualStress,
  WristBilateralXRaySingleView: C80DocTypecodes_WristBilateralXRaySingleView,
  PelvisXRayAPSingleViewStanding: C80DocTypecodes_PelvisXRayAPSingleViewStanding,
  FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfUnspecifiedBodyRegion: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfUnspecifiedBodyRegion,
  FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfBreast: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfBreast,
  FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfChest: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfChest,
  SpineThoracicAndLumbarXRayScoliosisAPAndLateralSitting: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPAndLateralSitting,
  SpineThoracicAndLumbarXRayScoliosisAPStandingAndWRightBendingAndWLeftBendingAndWOBending: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPStandingAndWRightBendingAndWLeftBendingAndWOBending,
  SpineThoracicAndLumbarXRayScoliosisAPSitting: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPSitting,
  SpineThoracicAndLumbarXRayScoliosisLateralSitting: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisLateralSitting,
  SpineThoracicAndLumbarXRayScoliosisAPStandingAndInBrace: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPStandingAndInBrace,
  SpineThoracicAndLumbarXRayScoliosisAPStandingAndWRightBending: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPStandingAndWRightBending,
  KneeRightXRay2ViewsAndViewsStanding: C80DocTypecodes_KneeRightXRay2ViewsAndViewsStanding,
  KneeRightXRay30DegreeStanding: C80DocTypecodes_KneeRightXRay30DegreeStanding,
  KneeRightXRaySunriseAndViewsStanding: C80DocTypecodes_KneeRightXRaySunriseAndViewsStanding,
  MammogramGuidanceStereotacticForCoreNeedleBiopsyOfBreastRight: C80DocTypecodes_MammogramGuidanceStereotacticForCoreNeedleBiopsyOfBreastRight,
  FootSesamoidBonesRightXRay: C80DocTypecodes_FootSesamoidBonesRightXRay,
  SellaTurcicaXRay2Views: C80DocTypecodes_SellaTurcicaXRay2Views,
  SellaTurcicaXRayLateralAndTowne: C80DocTypecodes_SellaTurcicaXRayLateralAndTowne,
  SellaTurcicaXRayTomograph: C80DocTypecodes_SellaTurcicaXRayTomograph,
  NeckXRayAPAndLateral: C80DocTypecodes_NeckXRayAPAndLateral,
  NeckXRayAPSingleView: C80DocTypecodes_NeckXRayAPSingleView,
  NeckXRayMagnification: C80DocTypecodes_NeckXRayMagnification,
  SpineXRayLateralStanding: C80DocTypecodes_SpineXRayLateralStanding,
  SpineThoracicXRay3ViewsStanding: C80DocTypecodes_SpineThoracicXRay3ViewsStanding,
  SpineThoracicXRayAPWRightBendingAndWLeftBendingAndWOBending: C80DocTypecodes_SpineThoracicXRayAPWRightBendingAndWLeftBendingAndWOBending,
  SpineThoracicXRayAPWLeftBendingAndWOBending: C80DocTypecodes_SpineThoracicXRayAPWLeftBendingAndWOBending,
  SpineThoracicXRayAPWRightBendingAndWOBending: C80DocTypecodes_SpineThoracicXRayAPWRightBendingAndWOBending,
  USGuidanceForAspirationOfCystOfThyroid: C80DocTypecodes_USGuidanceForAspirationOfCystOfThyroid,
  USGuidanceForExcisionalBiopsyOfBreast: C80DocTypecodes_USGuidanceForExcisionalBiopsyOfBreast,
  USGuidanceForBiopsyOfBreastLeft: C80DocTypecodes_USGuidanceForBiopsyOfBreastLeft,
  USGuidanceForAspirationOfCystOfBreastLeft: C80DocTypecodes_USGuidanceForAspirationOfCystOfBreastLeft,
  PelvisUSTransabdominalAndTransvaginal: C80DocTypecodes_PelvisUSTransabdominalAndTransvaginal,
  USGuidanceForPlacementOfNeedleWireInBreast: C80DocTypecodes_USGuidanceForPlacementOfNeedleWireInBreast,
  USGuidanceForBiopsyOfBreastRight: C80DocTypecodes_USGuidanceForBiopsyOfBreastRight,
  USGuidanceForAspirationOfCystOfBreastRight: C80DocTypecodes_USGuidanceForAspirationOfCystOfBreastRight,
  GastrointestineUpperFluoroscopyWContrastPO: C80DocTypecodes_GastrointestineUpperFluoroscopyWContrastPO,
  SubmandibularGlandLeftFluoroscopyWContrastIntraSalivaryDuct: C80DocTypecodes_SubmandibularGlandLeftFluoroscopyWContrastIntraSalivaryDuct,
  LowerExtremityVesselGraftLeftUSDoppler: C80DocTypecodes_LowerExtremityVesselGraftLeftUSDoppler,
  LowerExtremityVesselGraftRightUSDoppler: C80DocTypecodes_LowerExtremityVesselGraftRightUSDoppler,
  USGuidanceForBiopsyOfEndomyocardium: C80DocTypecodes_USGuidanceForBiopsyOfEndomyocardium,
  SurgicalSpecimenUS: C80DocTypecodes_SurgicalSpecimenUS,
  GastrointestineUpperAndSmallBowelAndGallbladderFluoroscopyWContrastPO: C80DocTypecodes_GastrointestineUpperAndSmallBowelAndGallbladderFluoroscopyWContrastPO,
  GastrointestineUpperAndGallbladderFluoroscopyWContrastPO: C80DocTypecodes_GastrointestineUpperAndGallbladderFluoroscopyWContrastPO,
  PelvisXRayStereo: C80DocTypecodes_PelvisXRayStereo,
  SpineThoracicAndLumbarXRayScoliosisAPInTraction: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPInTraction,
  SinusesXRayWatersStereo: C80DocTypecodes_SinusesXRayWatersStereo,
  SkullXRayStereo: C80DocTypecodes_SkullXRayStereo,
  UpperExtremityVesselGraftLeftUSDoppler: C80DocTypecodes_UpperExtremityVesselGraftLeftUSDoppler,
  UpperExtremityVesselGraftRightUSDoppler: C80DocTypecodes_UpperExtremityVesselGraftRightUSDoppler,
  KidneyVesselsTransplantUSDoppler: C80DocTypecodes_KidneyVesselsTransplantUSDoppler,
  USGuidanceForDrainageOfCystOfKidney: C80DocTypecodes_USGuidanceForDrainageOfCystOfKidney,
  ContributingFactorCommunicationDocumentationMERSTH: C80DocTypecodes_ContributingFactorCommunicationDocumentationMERSTH,
  BreastMammogramXCCL: C80DocTypecodes_BreastMammogramXCCL,
  ColonFluoroscopyWGastrografinPR: C80DocTypecodes_ColonFluoroscopyWGastrografinPR,
  GastrointestineUpperFluoroscopyWBariumContrastPO: C80DocTypecodes_GastrointestineUpperFluoroscopyWBariumContrastPO,
  GastrointestineUpperFluoroscopyWGastrografinPO: C80DocTypecodes_GastrointestineUpperFluoroscopyWGastrografinPO,
  PelvisAndHipLeftXRay2Views: C80DocTypecodes_PelvisAndHipLeftXRay2Views,
  PelvisAndHipRightXRay2Views: C80DocTypecodes_PelvisAndHipRightXRay2Views,
  RibsBilateralXRay2Views: C80DocTypecodes_RibsBilateralXRay2Views,
  CTGuidanceForAnestheticBlockInjectionOfSpine: C80DocTypecodes_CTGuidanceForAnestheticBlockInjectionOfSpine,
  SpineXRayObliqueSingleView: C80DocTypecodes_SpineXRayObliqueSingleView,
  SpineXRayWFlexionAndWExtension: C80DocTypecodes_SpineXRayWFlexionAndWExtension,
  SpineCervicalXRay6Views: C80DocTypecodes_SpineCervicalXRay6Views,
  SpineThoracicAndLumbarXRay: C80DocTypecodes_SpineThoracicAndLumbarXRay,
  UrinaryBladderAndUrethraMRICine: C80DocTypecodes_UrinaryBladderAndUrethraMRICine,
  ClavicleMRIWContrastIV: C80DocTypecodes_ClavicleMRIWContrastIV,
  LowerLegBilateralMRIWContrastIV: C80DocTypecodes_LowerLegBilateralMRIWContrastIV,
  LowerLegBilateralMRI: C80DocTypecodes_LowerLegBilateralMRI,
  LowerLegBilateralMRIWAndWOContrastIV: C80DocTypecodes_LowerLegBilateralMRIWAndWOContrastIV,
  SpineCervicalAndThoracicAndLumbarMRI: C80DocTypecodes_SpineCervicalAndThoracicAndLumbarMRI,
  ChestAndAbdomenXRaySingleView: C80DocTypecodes_ChestAndAbdomenXRaySingleView,
  BoneScanWTc99mTaggedWBCIV: C80DocTypecodes_BoneScanWTc99mTaggedWBCIV,
  CTGuidanceForLocalizationOfPlacentaOfUterus: C80DocTypecodes_CTGuidanceForLocalizationOfPlacentaOfUterus,
  UnspecifiedBodyRegionFluoroscopyGreaterThan1Hour: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyGreaterThan1Hour,
  UnspecifiedBodyRegionFluoroscopyLessThan1Hour: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopyLessThan1Hour,
  USGuidanceForAbscessDrainageOfAppendix: C80DocTypecodes_USGuidanceForAbscessDrainageOfAppendix,
  USGuidanceForInjectionOfPleuralSpace: C80DocTypecodes_USGuidanceForInjectionOfPleuralSpace,
  HeartUSLimited: C80DocTypecodes_HeartUSLimited,
  ScanWIn111TiuxetanIV: C80DocTypecodes_ScanWIn111TiuxetanIV,
  LiverScanBloodPool: C80DocTypecodes_LiverScanBloodPool,
  SpineCervicalXRayLimited: C80DocTypecodes_SpineCervicalXRayLimited,
  ScanWholeBodyWIn111TaggedWBCIV: C80DocTypecodes_ScanWholeBodyWIn111TaggedWBCIV,
  AVShuntScan: C80DocTypecodes_AVShuntScan,
  ClinicalTrialProtocolTrialName: C80DocTypecodes_ClinicalTrialProtocolTrialName,
  WristRightXRayScaphoidSingleView: C80DocTypecodes_WristRightXRayScaphoidSingleView,
  WristXRayScaphoidSingleView: C80DocTypecodes_WristXRayScaphoidSingleView,
  WristBilateralXRayScaphoidSingleView: C80DocTypecodes_WristBilateralXRayScaphoidSingleView,
  WristLeftXRayScaphoidSingleView: C80DocTypecodes_WristLeftXRayScaphoidSingleView,
  VAL11DeoxycorticosteroneMassVolumeInUrine: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInUrine,
  CTGuidanceForPercutaneousDrainageOfAbscessOfCavity: C80DocTypecodes_CTGuidanceForPercutaneousDrainageOfAbscessOfCavity,
  PulmonarySystemCT: C80DocTypecodes_PulmonarySystemCT,
  CTForTumorWholeBody: C80DocTypecodes_CTForTumorWholeBody,
  MammogramGuidanceForNeedleBiopsyOfBreastRight: C80DocTypecodes_MammogramGuidanceForNeedleBiopsyOfBreastRight,
  LiverMRIWAndWOFerumoxidesIV: C80DocTypecodes_LiverMRIWAndWOFerumoxidesIV,
  AnkleRightMRIDynamicWContrastIV: C80DocTypecodes_AnkleRightMRIDynamicWContrastIV,
  ElbowLeftMRIDynamicWContrastIV: C80DocTypecodes_ElbowLeftMRIDynamicWContrastIV,
  ElbowRightMRIDynamicWContrastIV: C80DocTypecodes_ElbowRightMRIDynamicWContrastIV,
  KneeLeftMRIDynamicWContrastIV: C80DocTypecodes_KneeLeftMRIDynamicWContrastIV,
  KneeRightMRIDynamicWContrastIV: C80DocTypecodes_KneeRightMRIDynamicWContrastIV,
  PulmonarySystemMRI: C80DocTypecodes_PulmonarySystemMRI,
  OropharynxMRI: C80DocTypecodes_OropharynxMRI,
  SpineCervicalAndSpineThoracicMRIWAndWOContrastIV: C80DocTypecodes_SpineCervicalAndSpineThoracicMRIWAndWOContrastIV,
  SpineCervicalAndSpineThoracicMRI: C80DocTypecodes_SpineCervicalAndSpineThoracicMRI,
  OrbitVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_OrbitVesselsMRIAngiogramWAndWOContrastIV,
  BrainScanDuringElectroconvulsiveShockTreatment: C80DocTypecodes_BrainScanDuringElectroconvulsiveShockTreatment,
  KidneyBilateralScanWFurosemideAndWRadionuclideIV: C80DocTypecodes_KidneyBilateralScanWFurosemideAndWRadionuclideIV,
  USGuidanceForNeedleBiopsyOfBreastLeft: C80DocTypecodes_USGuidanceForNeedleBiopsyOfBreastLeft,
  ChestAndAbdomenXRayAPSupineAndUprightAndPAChest: C80DocTypecodes_ChestAndAbdomenXRayAPSupineAndUprightAndPAChest,
  RibsBilateralAndChestXRayLateralAndPAChest: C80DocTypecodes_RibsBilateralAndChestXRayLateralAndPAChest,
  ChestXRayAPRightLateralDecubitus: C80DocTypecodes_ChestXRayAPRightLateralDecubitus,
  ChestXRay2ViewsAndRightObliqueAndLeftOblique: C80DocTypecodes_ChestXRay2ViewsAndRightObliqueAndLeftOblique,
  UnspecifiedBodyRegionXRay: C80DocTypecodes_UnspecifiedBodyRegionXRay,
  UnspecifiedBodyRegionXRayOfForeignBody: C80DocTypecodes_UnspecifiedBodyRegionXRayOfForeignBody,
  SkullXRayLE3Views: C80DocTypecodes_SkullXRayLE3Views,
  UnspecifiedBodyRegionFluoroscopy2Hour: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy2Hour,
  UnspecifiedBodyRegionFluoroscopy90Minutes: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy90Minutes,
  FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct2HoursPostContrastRetrograde: C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct2HoursPostContrastRetrograde,
  FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct15MinutesPostContrastRetrograde: C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct15MinutesPostContrastRetrograde,
  FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct30MinutesPostContrastRetrograde: C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct30MinutesPostContrastRetrograde,
  FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct45MinutesPostContrastRetrograde: C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct45MinutesPostContrastRetrograde,
  FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct1HourPostContrastRetrograde: C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct1HourPostContrastRetrograde,
  FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct15HoursPostContrastRetrograde: C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsAndPancreaticDuct15HoursPostContrastRetrograde,
  AortaAbdominalFluoroscopicAngiogramRunoffWContrastIA: C80DocTypecodes_AortaAbdominalFluoroscopicAngiogramRunoffWContrastIA,
  JointXRayLateralWManualStress: C80DocTypecodes_JointXRayLateralWManualStress,
  JointXRayWFlexionAndWExtension: C80DocTypecodes_JointXRayWFlexionAndWExtension,
  KneeRightXRayGE3Views: C80DocTypecodes_KneeRightXRayGE3Views,
  FootRightXRay3Or4Views: C80DocTypecodes_FootRightXRay3Or4Views,
  KidneyXRayDuringSurgeryWContrastRetrograde: C80DocTypecodes_KidneyXRayDuringSurgeryWContrastRetrograde,
  SinusesXRayGE3Views: C80DocTypecodes_SinusesXRayGE3Views,
  USGuidanceForPlacementOfRadiationTherapyFieldsInUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForPlacementOfRadiationTherapyFieldsInUnspecifiedBodyRegion,
  ThumbLeftXRayGE3Views: C80DocTypecodes_ThumbLeftXRayGE3Views,
  FingerSecondLeftXRayGE3Views: C80DocTypecodes_FingerSecondLeftXRayGE3Views,
  FingerThirdLeftXRayGE3Views: C80DocTypecodes_FingerThirdLeftXRayGE3Views,
  FingerFourthLeftXRayGE3Views: C80DocTypecodes_FingerFourthLeftXRayGE3Views,
  FingerFifthLeftXRayGE3Views: C80DocTypecodes_FingerFifthLeftXRayGE3Views,
  ThumbRightXRayGE3Views: C80DocTypecodes_ThumbRightXRayGE3Views,
  FingerSecondRightXRayGE3Views: C80DocTypecodes_FingerSecondRightXRayGE3Views,
  FingerThirdRightXRayGE3Views: C80DocTypecodes_FingerThirdRightXRayGE3Views,
  FingerFourthRightXRayGE3Views: C80DocTypecodes_FingerFourthRightXRayGE3Views,
  FingerFifthRightXRayGE3Views: C80DocTypecodes_FingerFifthRightXRayGE3Views,
  KneeLeftXRayGE3Views: C80DocTypecodes_KneeLeftXRayGE3Views,
  FootLeftXRay3Or4Views: C80DocTypecodes_FootLeftXRay3Or4Views,
  VesselScanFlow: C80DocTypecodes_VesselScanFlow,
  VesselScanStatic: C80DocTypecodes_VesselScanStatic,
  CTGuidanceForAbscessDrainageOfSubphrenicSpace: C80DocTypecodes_CTGuidanceForAbscessDrainageOfSubphrenicSpace,
  AortaAndLowerExtremityVesselsCTAngiogramWContrastIV: C80DocTypecodes_AortaAndLowerExtremityVesselsCTAngiogramWContrastIV,
  AxillaLeftMRIWContrastIV: C80DocTypecodes_AxillaLeftMRIWContrastIV,
  AxillaRightMRIWContrastIV: C80DocTypecodes_AxillaRightMRIWContrastIV,
  OvaryBilateralMRI: C80DocTypecodes_OvaryBilateralMRI,
  ThymusGlandMRI: C80DocTypecodes_ThymusGlandMRI,
  AxillaLeftMRI: C80DocTypecodes_AxillaLeftMRI,
  AxillaLeftMRIWAndWOContrastIV: C80DocTypecodes_AxillaLeftMRIWAndWOContrastIV,
  AxillaRightMRI: C80DocTypecodes_AxillaRightMRI,
  AxillaRightMRIWAndWOContrastIV: C80DocTypecodes_AxillaRightMRIWAndWOContrastIV,
  LowerLegVesselsBilateralMRIAngiogramWContrastIV: C80DocTypecodes_LowerLegVesselsBilateralMRIAngiogramWContrastIV,
  LowerLegVesselsLeftMRIAngiogram: C80DocTypecodes_LowerLegVesselsLeftMRIAngiogram,
  ThighVesselsLeftMRIAngiogramWOContrast: C80DocTypecodes_ThighVesselsLeftMRIAngiogramWOContrast,
  ThighVesselsRightMRIAngiogramWOContrast: C80DocTypecodes_ThighVesselsRightMRIAngiogramWOContrast,
  WristVesselsLeftMRIAngiogramWOContrast: C80DocTypecodes_WristVesselsLeftMRIAngiogramWOContrast,
  WristVesselsRightMRIAngiogramWOContrast: C80DocTypecodes_WristVesselsRightMRIAngiogramWOContrast,
  BonesXRaySurvey: C80DocTypecodes_BonesXRaySurvey,
  BonesXRaySurveyLimited: C80DocTypecodes_BonesXRaySurveyLimited,
  MandibleXRay1Or2Views: C80DocTypecodes_MandibleXRay1Or2Views,
  PelvisXRay1Or2Views: C80DocTypecodes_PelvisXRay1Or2Views,
  SinusesXRay1Or2Views: C80DocTypecodes_SinusesXRay1Or2Views,
  SkullXRayGE5Views: C80DocTypecodes_SkullXRayGE5Views,
  UnspecifiedBodyRegionCTWOContrast: C80DocTypecodes_UnspecifiedBodyRegionCTWOContrast,
  UnspecifiedBodyRegionSPECT: C80DocTypecodes_UnspecifiedBodyRegionSPECT,
  UnspecifiedBodyRegionCTDynamicWContrastIV: C80DocTypecodes_UnspecifiedBodyRegionCTDynamicWContrastIV,
  BreastUnilateralMRIWAndWOContrastIV: C80DocTypecodes_BreastUnilateralMRIWAndWOContrastIV,
  OrbitPlusFacialBonesXRay: C80DocTypecodes_OrbitPlusFacialBonesXRay,
  OrbitAndFaceAndNeckMRI: C80DocTypecodes_OrbitAndFaceAndNeckMRI,
  ChestAndAbdomenXRayUprightAndPAChest: C80DocTypecodes_ChestAndAbdomenXRayUprightAndPAChest,
  MandibleRightXRay: C80DocTypecodes_MandibleRightXRay,
  MandibleLeftXRay4Views: C80DocTypecodes_MandibleLeftXRay4Views,
  MandibleRightXRay4Views: C80DocTypecodes_MandibleRightXRay4Views,
  SpineLumbarFluoroscopyVideo: C80DocTypecodes_SpineLumbarFluoroscopyVideo,
  FluoroscopyGuidanceForDrainageOfUnspecifiedBodyRegion: C80DocTypecodes_FluoroscopyGuidanceForDrainageOfUnspecifiedBodyRegion,
  SpineCervicalFluoroscopyVideo: C80DocTypecodes_SpineCervicalFluoroscopyVideo,
  SpineCervicalXRay2Or3Views: C80DocTypecodes_SpineCervicalXRay2Or3Views,
  PelvisXRayGE3Views: C80DocTypecodes_PelvisXRayGE3Views,
  BrainScanStaticAndFlow: C80DocTypecodes_BrainScanStaticAndFlow,
  CarotidArteryUnilateralUS: C80DocTypecodes_CarotidArteryUnilateralUS,
  VesselsLeftFluoroscopicAngiogramWContrastIV: C80DocTypecodes_VesselsLeftFluoroscopicAngiogramWContrastIV,
  AnkleLeftMRIDynamicWContrastIV: C80DocTypecodes_AnkleLeftMRIDynamicWContrastIV,
  LowerLegVesselsRightMRIAngiogram: C80DocTypecodes_LowerLegVesselsRightMRIAngiogram,
  LiverAndBiliaryDuctsAndGallbladderScan: C80DocTypecodes_LiverAndBiliaryDuctsAndGallbladderScan,
  FluoroscopyGuidanceForChangeOfDialysisCatheterInUnspecifiedBodyRegionWContrastIV: C80DocTypecodes_FluoroscopyGuidanceForChangeOfDialysisCatheterInUnspecifiedBodyRegionWContrastIV,
  UrinaryBladderAndUrethraFluoroscopyWContrastIntraBladderDuringVoiding: C80DocTypecodes_UrinaryBladderAndUrethraFluoroscopyWContrastIntraBladderDuringVoiding,
  ChestAndAbdomenXRayAPUprightAndAPChest: C80DocTypecodes_ChestAndAbdomenXRayAPUprightAndAPChest,
  SkeletalSystemAxialScanBoneDensity: C80DocTypecodes_SkeletalSystemAxialScanBoneDensity,
  SkeletalSystemPeripheralScanBoneDensity: C80DocTypecodes_SkeletalSystemPeripheralScanBoneDensity,
  USGuidanceForBiopsyOfSuperficialMuscle: C80DocTypecodes_USGuidanceForBiopsyOfSuperficialMuscle,
  USGuidanceForBiopsyOfDeepBone: C80DocTypecodes_USGuidanceForBiopsyOfDeepBone,
  HipAndThighUS: C80DocTypecodes_HipAndThighUS,
  CTGuidanceForBiopsyOfDeepBone: C80DocTypecodes_CTGuidanceForBiopsyOfDeepBone,
  CTGuidanceForNeedleBiopsyOfDeepBone: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfDeepBone,
  SpineThoracicAndLumbarXRayScoliosisAPUprightAndSupine: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisAPUprightAndSupine,
  HandXRayPortable: C80DocTypecodes_HandXRayPortable,
  CTGuidanceForNeedleBiopsyOfSoftBone: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfSoftBone,
  AbdominalVesselsUSDopplerLimited: C80DocTypecodes_AbdominalVesselsUSDopplerLimited,
  UpperGastrointestineAndSmallBowelFluoroscopyWBariumContrastPO: C80DocTypecodes_UpperGastrointestineAndSmallBowelFluoroscopyWBariumContrastPO,
  FootSesamoidBonesLeftXRay: C80DocTypecodes_FootSesamoidBonesLeftXRay,
  BrainScanFlowWTc99mDTPAIV: C80DocTypecodes_BrainScanFlowWTc99mDTPAIV,
  BrainScanFlowWTc99mGlucoheptonateIV: C80DocTypecodes_BrainScanFlowWTc99mGlucoheptonateIV,
  BrainScanFlowLimited: C80DocTypecodes_BrainScanFlowLimited,
  HeartScanForInfarctQualitative: C80DocTypecodes_HeartScanForInfarctQualitative,
  HeartScanForInfarctQualitativeAndQuantitative: C80DocTypecodes_HeartScanForInfarctQualitativeAndQuantitative,
  HeartScanForInfarctQuantitative: C80DocTypecodes_HeartScanForInfarctQuantitative,
  ScanForEndocrineTumorMultipleAreasWI131MIBGIV: C80DocTypecodes_ScanForEndocrineTumorMultipleAreasWI131MIBGIV,
  ScanForEndocrineTumorMultipleAreasWIn111PentetreotideIV: C80DocTypecodes_ScanForEndocrineTumorMultipleAreasWIn111PentetreotideIV,
  LiverAndBiliaryDuctsAndGallbladderScanWCholecystokininAndWRadionuclideIV: C80DocTypecodes_LiverAndBiliaryDuctsAndGallbladderScanWCholecystokininAndWRadionuclideIV,
  LiverAndBiliaryDuctsAndGallbladderScanWSincalideAndWRadionuclideIV: C80DocTypecodes_LiverAndBiliaryDuctsAndGallbladderScanWSincalideAndWRadionuclideIV,
  LiverAndSpleenSPECTFlow: C80DocTypecodes_LiverAndSpleenSPECTFlow,
  LiverAndSpleenScanFlow: C80DocTypecodes_LiverAndSpleenScanFlow,
  LiverScanFlowWTc99mTaggedRBCIV: C80DocTypecodes_LiverScanFlowWTc99mTaggedRBCIV,
  LiverSPECTFlow: C80DocTypecodes_LiverSPECTFlow,
  LungScanPerfusionQuantitative: C80DocTypecodes_LungScanPerfusionQuantitative,
  LungScanQuantitative: C80DocTypecodes_LungScanQuantitative,
  HeartScanPerfusionQuantitative: C80DocTypecodes_HeartScanPerfusionQuantitative,
  HeartSPECTPerfusionQualitativeAtRestAndWRadionuclideIV: C80DocTypecodes_HeartSPECTPerfusionQualitativeAtRestAndWRadionuclideIV,
  HeartScanPerfusionQualitativeAtRestAndWRadionuclideIV: C80DocTypecodes_HeartScanPerfusionQualitativeAtRestAndWRadionuclideIV,
  HeartScanPerfusionQuantitativeAtRestAndWRadionuclideIV: C80DocTypecodes_HeartScanPerfusionQuantitativeAtRestAndWRadionuclideIV,
  RenalVesselsSPECTFlowWTc99mGlucoheptonateIV: C80DocTypecodes_RenalVesselsSPECTFlowWTc99mGlucoheptonateIV,
  RenalVesselsScanFlowWTc99mGlucoheptonateIV: C80DocTypecodes_RenalVesselsScanFlowWTc99mGlucoheptonateIV,
  RenalVesselsScanFlowWTc99mDTPAIV: C80DocTypecodes_RenalVesselsScanFlowWTc99mDTPAIV,
  RenalVesselsScanFlowWTc99mMertiatideIV: C80DocTypecodes_RenalVesselsScanFlowWTc99mMertiatideIV,
  KidneyBilateralAndRenalVesselsScanFlowWTc99mGlucoheptonateIV: C80DocTypecodes_KidneyBilateralAndRenalVesselsScanFlowWTc99mGlucoheptonateIV,
  KidneyBilateralAndRenalVesselsScanWTc99mDTPAIV: C80DocTypecodes_KidneyBilateralAndRenalVesselsScanWTc99mDTPAIV,
  RenalVesselsScan: C80DocTypecodes_RenalVesselsScan,
  SpleenSPECTFlow: C80DocTypecodes_SpleenSPECTFlow,
  ThyroidScanSpot: C80DocTypecodes_ThyroidScanSpot,
  ThyroidScanAndUptake: C80DocTypecodes_ThyroidScanAndUptake,
  ThyroidSPECTFlow: C80DocTypecodes_ThyroidSPECTFlow,
  FDAPackageInsertDosageFormsAndStrengthsSection: C80DocTypecodes_FDAPackageInsertDosageFormsAndStrengthsSection,
  FDAPackageInsertMechanismOfActionSection: C80DocTypecodes_FDAPackageInsertMechanismOfActionSection,
  FDAPackageInsertNonclinicalToxicologySection: C80DocTypecodes_FDAPackageInsertNonclinicalToxicologySection,
  FDAPackageInsertPharmacodynamicsSection: C80DocTypecodes_FDAPackageInsertPharmacodynamicsSection,
  FDAPackageInsertPharmacokineticsSection: C80DocTypecodes_FDAPackageInsertPharmacokineticsSection,
  FDAPackageInsertRecentMajorChangesSection: C80DocTypecodes_FDAPackageInsertRecentMajorChangesSection,
  FDAPackageInsertUseInSpecificPopulationsSection: C80DocTypecodes_FDAPackageInsertUseInSpecificPopulationsSection,
  FDAPackageInsertWarningsAndPrecautionsSection: C80DocTypecodes_FDAPackageInsertWarningsAndPrecautionsSection,
  USGuidanceForAspirationOfBreast: C80DocTypecodes_USGuidanceForAspirationOfBreast,
  CTGuidanceForFineNeedleAspirationOfKidney: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfKidney,
  USGuidanceForLocalizationOfBreastLeft: C80DocTypecodes_USGuidanceForLocalizationOfBreastLeft,
  USGuidanceForLocalizationOfBreastBilateral: C80DocTypecodes_USGuidanceForLocalizationOfBreastBilateral,
  USGuidanceForLocalizationOfBreastRight: C80DocTypecodes_USGuidanceForLocalizationOfBreastRight,
  FluoroscopicAngiogramGuidanceForThrombectomyOfVeinBilateralWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForThrombectomyOfVeinBilateralWContrastIV,
  FluoroscopicAngiogramGuidanceForThrombectomyOfVeinLeftWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForThrombectomyOfVeinLeftWContrastIV,
  FluoroscopicAngiogramGuidanceForThrombectomyOfVeinWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForThrombectomyOfVeinWContrastIV,
  FluoroscopicAngiogramGuidanceForThrombectomyOfVeinRightWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForThrombectomyOfVeinRightWContrastIV,
  CarotidArteryBilateralUSDoppler: C80DocTypecodes_CarotidArteryBilateralUSDoppler,
  KidneyBilateralCTWContrastIV: C80DocTypecodes_KidneyBilateralCTWContrastIV,
  KidneyBilateralCT: C80DocTypecodes_KidneyBilateralCT,
  KidneyCTWAndWOContrastIV: C80DocTypecodes_KidneyCTWAndWOContrastIV,
  BrainAndInternalAuditoryCanalMRIWAndWOContrastIV: C80DocTypecodes_BrainAndInternalAuditoryCanalMRIWAndWOContrastIV,
  KidneyCTWOContrast: C80DocTypecodes_KidneyCTWOContrast,
  ExtremityVesselsUSDoppler: C80DocTypecodes_ExtremityVesselsUSDoppler,
  BrainAndInternalAuditoryCanalMRI: C80DocTypecodes_BrainAndInternalAuditoryCanalMRI,
  KidneyMRIWOContrast: C80DocTypecodes_KidneyMRIWOContrast,
  KidneyBilateralUS: C80DocTypecodes_KidneyBilateralUS,
  KidneyMRIWAndWOContrastIV: C80DocTypecodes_KidneyMRIWAndWOContrastIV,
  IliacArteryUSDopplerLimited: C80DocTypecodes_IliacArteryUSDopplerLimited,
  HeartScanPerfusionAtRestAndWAdenosineAndWTl201IV: C80DocTypecodes_HeartScanPerfusionAtRestAndWAdenosineAndWTl201IV,
  ChestXRayAPSupinePortable: C80DocTypecodes_ChestXRayAPSupinePortable,
  KneeLeftXRaySunrise: C80DocTypecodes_KneeLeftXRaySunrise,
  KneeXRaySunrise: C80DocTypecodes_KneeXRaySunrise,
  SpineCervicothoracicJunctionXRay: C80DocTypecodes_SpineCervicothoracicJunctionXRay,
  IliacArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_IliacArteryFluoroscopicAngiogramWContrastIA,
  RenalVeinFluoroscopicAngiogramWContrastIVAndWReninSampling: C80DocTypecodes_RenalVeinFluoroscopicAngiogramWContrastIVAndWReninSampling,
  SpineCervicalAndThoracicAndLumbarXRay2Views: C80DocTypecodes_SpineCervicalAndThoracicAndLumbarXRay2Views,
  SpineCervicothoracicJunctionXRayAPAndLateral: C80DocTypecodes_SpineCervicothoracicJunctionXRayAPAndLateral,
  SkullAndFacialBonesAndMandibleXRayForDentalMeasurement: C80DocTypecodes_SkullAndFacialBonesAndMandibleXRayForDentalMeasurement,
  TubeFluoroscopyForPatencyWContrastViaTube: C80DocTypecodes_TubeFluoroscopyForPatencyWContrastViaTube,
  LiverAndBiliaryDuctsAndGallbladderScanForPatencyWTc99mIV: C80DocTypecodes_LiverAndBiliaryDuctsAndGallbladderScanForPatencyWTc99mIV,
  ShoulderRightXRayGrasheyAndY: C80DocTypecodes_ShoulderRightXRayGrasheyAndY,
  SpineLumbarXRayOblique: C80DocTypecodes_SpineLumbarXRayOblique,
  TibioperonealArteriesRightFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_TibioperonealArteriesRightFluoroscopicAngiogramAngioplastyWContrastIA,
  TibioperonealArteriesFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_TibioperonealArteriesFluoroscopicAngiogramAngioplastyWContrastIA,
  TibioperonealArteriesBilateralFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_TibioperonealArteriesBilateralFluoroscopicAngiogramAngioplastyWContrastIA,
  TibioperonealArteriesLeftFluoroscopicAngiogramAngioplastyWContrastIA: C80DocTypecodes_TibioperonealArteriesLeftFluoroscopicAngiogramAngioplastyWContrastIA,
  WristBilateralXRayTunnelCarpal: C80DocTypecodes_WristBilateralXRayTunnelCarpal,
  USGuidanceForBiopsyOfSuperficialLymphNode: C80DocTypecodes_USGuidanceForBiopsyOfSuperficialLymphNode,
  CTGuidanceForAblationOfTissueOfLiver: C80DocTypecodes_CTGuidanceForAblationOfTissueOfLiver,
  CTGuidanceForProcedureOfJointSpace: C80DocTypecodes_CTGuidanceForProcedureOfJointSpace,
  CTGuidanceForFineNeedleAspirationOfLymphNode: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfLymphNode,
  CTGuidanceForFineNeedleAspirationOfMediastinum: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfMediastinum,
  CTGuidanceForFineNeedleAspirationOfMuscle: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfMuscle,
  CTGuidanceForFineNeedleAspirationOfProstate: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfProstate,
  CTGuidanceForFineNeedleAspirationOfAbdomenRetroperitoneum: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfAbdomenRetroperitoneum,
  CTGuidanceForFineNeedleAspirationOfAdrenalGland: C80DocTypecodes_CTGuidanceForFineNeedleAspirationOfAdrenalGland,
  CTGuidanceForBiopsyOfDeepMuscle: C80DocTypecodes_CTGuidanceForBiopsyOfDeepMuscle,
  CTGuidanceForNeedleLocalizationOfBreast: C80DocTypecodes_CTGuidanceForNeedleLocalizationOfBreast,
  SkullBaseCTWAndWOContrastIV: C80DocTypecodes_SkullBaseCTWAndWOContrastIV,
  SkullBaseCTWOContrast: C80DocTypecodes_SkullBaseCTWOContrast,
  SpineThoracicCTWAndWOContrastIT: C80DocTypecodes_SpineThoracicCTWAndWOContrastIT,
  SpineLumbarCTWAndWOContrastIT: C80DocTypecodes_SpineLumbarCTWAndWOContrastIT,
  AbdomenAndPelvisCT: C80DocTypecodes_AbdomenAndPelvisCT,
  MandibleCTLimited: C80DocTypecodes_MandibleCTLimited,
  CTGuidanceForBiopsyOfAbdomenRetroperitoneum: C80DocTypecodes_CTGuidanceForBiopsyOfAbdomenRetroperitoneum,
  CTGuidanceForNeedleLocalizationOfBreastWAndWOContrastIV: C80DocTypecodes_CTGuidanceForNeedleLocalizationOfBreastWAndWOContrastIV,
  BreastBilateralCTWOContrast: C80DocTypecodes_BreastBilateralCTWOContrast,
  ColonCT: C80DocTypecodes_ColonCT,
  MammogramGuidanceForPercutaneousNeedleBiopsyOfBreast: C80DocTypecodes_MammogramGuidanceForPercutaneousNeedleBiopsyOfBreast,
  MRIGuidanceStereotacticForLocalizationInBrainWAndWOContrastIV: C80DocTypecodes_MRIGuidanceStereotacticForLocalizationInBrainWAndWOContrastIV,
  BiliaryDuctsAndPancreaticDuctMRIWOContrast: C80DocTypecodes_BiliaryDuctsAndPancreaticDuctMRIWOContrast,
  AdrenalGlandMRIWContrastIV: C80DocTypecodes_AdrenalGlandMRIWContrastIV,
  BiliaryDuctsAndPancreaticDuctMRIWContrastIV: C80DocTypecodes_BiliaryDuctsAndPancreaticDuctMRIWContrastIV,
  HeartMRICineForBloodFlowVelocityMappingWContrastIV: C80DocTypecodes_HeartMRICineForBloodFlowVelocityMappingWContrastIV,
  HeartMRILimitedCineForFunction: C80DocTypecodes_HeartMRILimitedCineForFunction,
  LowerExtremityVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_LowerExtremityVesselsMRIAngiogramWAndWOContrastIV,
  LowerExtremityVesselsMRIAngiogramWOContrast: C80DocTypecodes_LowerExtremityVesselsMRIAngiogramWOContrast,
  AorticArchMRIAngiogramWOContrast: C80DocTypecodes_AorticArchMRIAngiogramWOContrast,
  AortaMRIAngiogramWAndWOContrastIV: C80DocTypecodes_AortaMRIAngiogramWAndWOContrastIV,
  AortaMRIAngiogramWOContrast: C80DocTypecodes_AortaMRIAngiogramWOContrast,
  RenalVesselsMRIAngiogramWOContrast: C80DocTypecodes_RenalVesselsMRIAngiogramWOContrast,
  RenalVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_RenalVesselsMRIAngiogramWAndWOContrastIV,
  LowerExtremityVesselsBilateralMRIAngiogramWContrastIV: C80DocTypecodes_LowerExtremityVesselsBilateralMRIAngiogramWContrastIV,
  UnspecifiedBodyRegionPET: C80DocTypecodes_UnspecifiedBodyRegionPET,
  HeartPET: C80DocTypecodes_HeartPET,
  BrainPET: C80DocTypecodes_BrainPET,
  PETWholeBody: C80DocTypecodes_PETWholeBody,
  AbdomenAndPelvisScanForTumor: C80DocTypecodes_AbdomenAndPelvisScanForTumor,
  LiverAndSpleenScanWTc99mMAAIV: C80DocTypecodes_LiverAndSpleenScanWTc99mMAAIV,
  BoneScanWTc99mMedronateIV: C80DocTypecodes_BoneScanWTc99mMedronateIV,
  HeartScanWTc99mTaggedRBCIV: C80DocTypecodes_HeartScanWTc99mTaggedRBCIV,
  LiverScanWXe133Inhaled: C80DocTypecodes_LiverScanWXe133Inhaled,
  ParathyroidScanWTc99mSestamibiIV: C80DocTypecodes_ParathyroidScanWTc99mSestamibiIV,
  BoneMarrowScanWTc99mSCIV: C80DocTypecodes_BoneMarrowScanWTc99mSCIV,
  ThyroidScanAndUptakeWTc99mPertechnetateIV: C80DocTypecodes_ThyroidScanAndUptakeWTc99mPertechnetateIV,
  BrainScanFlowWTc99mBicisateIV: C80DocTypecodes_BrainScanFlowWTc99mBicisateIV,
  PeritoneovenousShuntScanForPatencyWTc99mMAAInj: C80DocTypecodes_PeritoneovenousShuntScanForPatencyWTc99mMAAInj,
  BrainScanStaticWTc99mBicisateIV: C80DocTypecodes_BrainScanStaticWTc99mBicisateIV,
  HeartSPECTWTc99mSestamibiIV: C80DocTypecodes_HeartSPECTWTc99mSestamibiIV,
  BrainSPECTWTc99mBicisateIV: C80DocTypecodes_BrainSPECTWTc99mBicisateIV,
  KidneySPECTWTc99mGlucoheptonateIV: C80DocTypecodes_KidneySPECTWTc99mGlucoheptonateIV,
  HeartSPECTWDipyridamoleAndWTc99mSestamibiIV: C80DocTypecodes_HeartSPECTWDipyridamoleAndWTc99mSestamibiIV,
  USGuidanceForAblationOfTissueOfLiver: C80DocTypecodes_USGuidanceForAblationOfTissueOfLiver,
  USGuidanceForAblationOfTissueOfKidney: C80DocTypecodes_USGuidanceForAblationOfTissueOfKidney,
  USGuidanceForFineNeedleAspirationOfPancreas: C80DocTypecodes_USGuidanceForFineNeedleAspirationOfPancreas,
  USGuidanceForFineNeedleAspirationOfLiver: C80DocTypecodes_USGuidanceForFineNeedleAspirationOfLiver,
  USGuidanceForFineNeedleAspirationOfKidney: C80DocTypecodes_USGuidanceForFineNeedleAspirationOfKidney,
  USGuidanceForFineNeedleAspirationOfBreast: C80DocTypecodes_USGuidanceForFineNeedleAspirationOfBreast,
  USGuidanceForBiopsyOfLung: C80DocTypecodes_USGuidanceForBiopsyOfLung,
  USGuidanceForBiopsyOfAbdomenRetroperitoneum: C80DocTypecodes_USGuidanceForBiopsyOfAbdomenRetroperitoneum,
  ThoracicOutletUS: C80DocTypecodes_ThoracicOutletUS,
  HeadAndNeckUS: C80DocTypecodes_HeadAndNeckUS,
  UnspecifiedBodyRegionUSAnd3DReconstruction: C80DocTypecodes_UnspecifiedBodyRegionUSAnd3DReconstruction,
  USGuidanceForAbscessDrainageOfSubphrenicSpace: C80DocTypecodes_USGuidanceForAbscessDrainageOfSubphrenicSpace,
  USGuidanceForAbscessDrainageOfKidney: C80DocTypecodes_USGuidanceForAbscessDrainageOfKidney,
  USGuidanceForAbscessDrainageOfPelvis: C80DocTypecodes_USGuidanceForAbscessDrainageOfPelvis,
  USGuidanceForAbscessDrainageOfPeritonealSpace: C80DocTypecodes_USGuidanceForAbscessDrainageOfPeritonealSpace,
  USGuidanceForNeedleBiopsyOfLiver: C80DocTypecodes_USGuidanceForNeedleBiopsyOfLiver,
  USGuidanceForNeedleBiopsyOfChestPleura: C80DocTypecodes_USGuidanceForNeedleBiopsyOfChestPleura,
  USGuidanceForDrainageOfPancreas: C80DocTypecodes_USGuidanceForDrainageOfPancreas,
  PeripheralArteryUSLimited: C80DocTypecodes_PeripheralArteryUSLimited,
  LowerExtremityVesselsUSDoppler: C80DocTypecodes_LowerExtremityVesselsUSDoppler,
  NeckVesselsUSDoppler: C80DocTypecodes_NeckVesselsUSDoppler,
  HipXRaySingleViewPortable: C80DocTypecodes_HipXRaySingleViewPortable,
  LowerExtremityBilateralXRayAPSingleViewStanding: C80DocTypecodes_LowerExtremityBilateralXRayAPSingleViewStanding,
  SpineLumbarXRayObliqueViewAndViewsWRightBendingAndWLeftBending: C80DocTypecodes_SpineLumbarXRayObliqueViewAndViewsWRightBendingAndWLeftBending,
  SacrumAndCoccyxXRay2Views: C80DocTypecodes_SacrumAndCoccyxXRay2Views,
  SacroiliacJointXRay2Or3Views: C80DocTypecodes_SacroiliacJointXRay2Or3Views,
  HandXRay2ViewsPortable: C80DocTypecodes_HandXRay2ViewsPortable,
  RadiusAndUlnaXRay2ViewsPortable: C80DocTypecodes_RadiusAndUlnaXRay2ViewsPortable,
  ElbowXRay2ViewsPortable: C80DocTypecodes_ElbowXRay2ViewsPortable,
  FemurXRayAPAndLateralPortable: C80DocTypecodes_FemurXRayAPAndLateralPortable,
  FootXRayAPAndLateralPortable: C80DocTypecodes_FootXRayAPAndLateralPortable,
  SpineCervicalXRayAPAndObliqueAndOdontoidAndLateralPortableWFlexionAndWExtension: C80DocTypecodes_SpineCervicalXRayAPAndObliqueAndOdontoidAndLateralPortableWFlexionAndWExtension,
  FootXRayGE3Views: C80DocTypecodes_FootXRayGE3Views,
  SacroiliacJointXRayGE3Views: C80DocTypecodes_SacroiliacJointXRayGE3Views,
  WristXRayGE3Views: C80DocTypecodes_WristXRayGE3Views,
  RibsAndChestXRayGE3AndPAChestViews: C80DocTypecodes_RibsAndChestXRayGE3AndPAChestViews,
  PelvisXRayGE3PortableViews: C80DocTypecodes_PelvisXRayGE3PortableViews,
  HandXRayGE3PortableViews: C80DocTypecodes_HandXRayGE3PortableViews,
  SpineXRayGE4ViewsWRightBendingAndWLeftBending: C80DocTypecodes_SpineXRayGE4ViewsWRightBendingAndWLeftBending,
  KneeXRayGE5Views: C80DocTypecodes_KneeXRayGE5Views,
  SpineLumbarXRayGE5ViewsWRightBendingAndWLeftBending: C80DocTypecodes_SpineLumbarXRayGE5ViewsWRightBendingAndWLeftBending,
  KneeBilateralXRayGE5ViewsStanding: C80DocTypecodes_KneeBilateralXRayGE5ViewsStanding,
  KneeXRay1Or2Views: C80DocTypecodes_KneeXRay1Or2Views,
  FacialBonesXRay1Or2Views: C80DocTypecodes_FacialBonesXRay1Or2Views,
  PelvisXRay1Or2ViewsPortable: C80DocTypecodes_PelvisXRay1Or2ViewsPortable,
  KneeXRay1Or2ViewsPortable: C80DocTypecodes_KneeXRay1Or2ViewsPortable,
  SpineCervicalAndThoracicAndLumbarXRayPortable: C80DocTypecodes_SpineCervicalAndThoracicAndLumbarXRayPortable,
  FluoroscopyGuidanceForPercutaneousNeedleBiopsyOfLung: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousNeedleBiopsyOfLung,
  LowerExtremityBilateralXRayStanding: C80DocTypecodes_LowerExtremityBilateralXRayStanding,
  SpineThoracicAndLumbarXRayScoliosisSingleView: C80DocTypecodes_SpineThoracicAndLumbarXRayScoliosisSingleView,
  OrbitXRayForForeignBody: C80DocTypecodes_OrbitXRayForForeignBody,
  SinusesXRayLimited: C80DocTypecodes_SinusesXRayLimited,
  AnkleXRayGE3Views: C80DocTypecodes_AnkleXRayGE3Views,
  ChestXRayGE4Views: C80DocTypecodes_ChestXRayGE4Views,
  SpineCervicalXRayGE4Views: C80DocTypecodes_SpineCervicalXRayGE4Views,
  FluoroscopyGuidanceForEndoscopyOfPancreaticDuctWContrastRetrograde: C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfPancreaticDuctWContrastRetrograde,
  FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsWContrastRetrograde: C80DocTypecodes_FluoroscopyGuidanceForEndoscopyOfBiliaryDuctsWContrastRetrograde,
  FluoroscopyGuidanceForFineNeedleAspirationOfUnspecifiedBodyRegion: C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfUnspecifiedBodyRegion,
  FluoroscopyGuidanceForFineNeedleAspirationOfThyroid: C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfThyroid,
  FluoroscopyGuidanceForFineNeedleAspirationOfKidney: C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfKidney,
  FluoroscopyGuidanceForFineNeedleAspirationOfPancreas: C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfPancreas,
  FluoroscopyGuidanceForFineNeedleAspirationOfLymphNode: C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfLymphNode,
  FluoroscopyGuidanceForFineNeedleAspirationOfLiver: C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfLiver,
  FluoroscopyGuidanceForFineNeedleAspirationOfDeepTissue: C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfDeepTissue,
  FluoroscopyGuidanceForProcedureOfJointSpace: C80DocTypecodes_FluoroscopyGuidanceForProcedureOfJointSpace,
  FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfOvary: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfOvary,
  FluoroscopyGuidanceForPlacementOfTubeInUnspecifiedBodyRegion: C80DocTypecodes_FluoroscopyGuidanceForPlacementOfTubeInUnspecifiedBodyRegion,
  FluoroscopyGuidanceForNeedleBiopsyOfLiverWContrastIV: C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfLiverWContrastIV,
  ColonFluoroscopyReductionWViewsWBariumContrastPR: C80DocTypecodes_ColonFluoroscopyReductionWViewsWBariumContrastPR,
  ColonFluoroscopyWBariumContrastPR: C80DocTypecodes_ColonFluoroscopyWBariumContrastPR,
  CTGuidanceForAblationOfTissueOfKidney: C80DocTypecodes_CTGuidanceForAblationOfTissueOfKidney,
  BonesCT: C80DocTypecodes_BonesCT,
  SuperiorMesentericVesselsMRIAngiogramWOContrast: C80DocTypecodes_SuperiorMesentericVesselsMRIAngiogramWOContrast,
  SuperiorMesentericVesselsMRIAngiogramWAndWOContrastIV: C80DocTypecodes_SuperiorMesentericVesselsMRIAngiogramWAndWOContrastIV,
  KidneyBilateralScanWAndWOTc99mMertiatideIV: C80DocTypecodes_KidneyBilateralScanWAndWOTc99mMertiatideIV,
  KidneyBilateralScanWAndWOTc99mDTPAIV: C80DocTypecodes_KidneyBilateralScanWAndWOTc99mDTPAIV,
  KidneyBilateralScanWTc99mGlucoheptonateIV: C80DocTypecodes_KidneyBilateralScanWTc99mGlucoheptonateIV,
  SuperiorMesentericVesselsUSDoppler: C80DocTypecodes_SuperiorMesentericVesselsUSDoppler,
  UpperExtremityVesselGraftBilateralUSDoppler: C80DocTypecodes_UpperExtremityVesselGraftBilateralUSDoppler,
  UpperExtremityVesselGraftBilateralUSDopplerLimited: C80DocTypecodes_UpperExtremityVesselGraftBilateralUSDopplerLimited,
  TracheaXRay: C80DocTypecodes_TracheaXRay,
  RibsUnilateralAndChestXRayGe3AndPAChestPortableViews: C80DocTypecodes_RibsUnilateralAndChestXRayGe3AndPAChestPortableViews,
  PeripheralArteriesBilateralFluoroscopicAngiogramWContrastIA: C80DocTypecodes_PeripheralArteriesBilateralFluoroscopicAngiogramWContrastIA,
  FDAPackageInsertStorageAndHandlingSection: C80DocTypecodes_FDAPackageInsertStorageAndHandlingSection,
  Progesterone11DeoxycorticosteroneMassRatioInSerumOrPlasma: C80DocTypecodes_Progesterone11DeoxycorticosteroneMassRatioInSerumOrPlasma,
  SelfManagement: C80DocTypecodes_SelfManagement,
  NursingNotes: C80DocTypecodes_NursingNotes,
  ProviderOrders: C80DocTypecodes_ProviderOrders,
  CaseManagerNote: C80DocTypecodes_CaseManagerNote,
  TiltTableStudy: C80DocTypecodes_TiltTableStudy,
  IntracardiacAblationStudy: C80DocTypecodes_IntracardiacAblationStudy,
  WoundCareManagementNote: C80DocTypecodes_WoundCareManagementNote,
  FetalDocumentVitalSignsMeasurements: C80DocTypecodes_FetalDocumentVitalSignsMeasurements,
  HistoryOfMedicalDeviceUse: C80DocTypecodes_HistoryOfMedicalDeviceUse,
  CTGuidanceForAspirationOrInjectionOfCystOfUnspecifiedBodyRegion: C80DocTypecodes_CTGuidanceForAspirationOrInjectionOfCystOfUnspecifiedBodyRegion,
  USGuidanceForAspirationOrInjectionOfCystOfUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForAspirationOrInjectionOfCystOfUnspecifiedBodyRegion,
  MammogramGuidanceForFineNeedleAspirationOfBreastRight: C80DocTypecodes_MammogramGuidanceForFineNeedleAspirationOfBreastRight,
  MammogramGuidanceForFineNeedleAspirationOfBreastLeft: C80DocTypecodes_MammogramGuidanceForFineNeedleAspirationOfBreastLeft,
  USGuidanceForCoreNeedleBiopsyOfThyroid: C80DocTypecodes_USGuidanceForCoreNeedleBiopsyOfThyroid,
  MammogramGuidanceForNeedleBiopsyOfBreast: C80DocTypecodes_MammogramGuidanceForNeedleBiopsyOfBreast,
  CTGuidanceForNeedleBiopsyOfUnspecifiedBodyRegion: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfUnspecifiedBodyRegion,
  USGuidanceForNeedleBiopsyOfProstate: C80DocTypecodes_USGuidanceForNeedleBiopsyOfProstate,
  CTGuidanceForBiopsyOfUnspecifiedBodyRegionWAndWOContrastIV: C80DocTypecodes_CTGuidanceForBiopsyOfUnspecifiedBodyRegionWAndWOContrastIV,
  CTGuidanceForBiopsyOfUnspecifiedBodyRegionWOContrast: C80DocTypecodes_CTGuidanceForBiopsyOfUnspecifiedBodyRegionWOContrast,
  CTGuidanceForDrainageOfUnspecifiedBodyRegionWAndWOContrastIV: C80DocTypecodes_CTGuidanceForDrainageOfUnspecifiedBodyRegionWAndWOContrastIV,
  CTGuidanceForDrainageOfUnspecifiedBodyRegionWContrastIV: C80DocTypecodes_CTGuidanceForDrainageOfUnspecifiedBodyRegionWContrastIV,
  CTGuidanceForDrainageOfUnspecifiedBodyRegionWOContrast: C80DocTypecodes_CTGuidanceForDrainageOfUnspecifiedBodyRegionWOContrast,
  FluoroscopyGuidanceForReplacementOfPercutaneousDrainageTubeInStomach: C80DocTypecodes_FluoroscopyGuidanceForReplacementOfPercutaneousDrainageTubeInStomach,
  MammogramGuidanceStereotacticForCoreNeedleBiopsyOfBreastLeft: C80DocTypecodes_MammogramGuidanceStereotacticForCoreNeedleBiopsyOfBreastLeft,
  MammogramGuidanceStereotacticForCoreNeedleBiopsyOfBreast: C80DocTypecodes_MammogramGuidanceStereotacticForCoreNeedleBiopsyOfBreast,
  SPECT: C80DocTypecodes_SPECT,
  MastoidBilateralCT: C80DocTypecodes_MastoidBilateralCT,
  BreastUnilateralMRI: C80DocTypecodes_BreastUnilateralMRI,
  SinusesCTCoronal: C80DocTypecodes_SinusesCTCoronal,
  ExtremityVeinBilateralUSDopplerLimited: C80DocTypecodes_ExtremityVeinBilateralUSDopplerLimited,
  UpperExtremityArteryBilateralUSDopplerLimited: C80DocTypecodes_UpperExtremityArteryBilateralUSDopplerLimited,
  UpperExtremityVesselsUSDopplerLimited: C80DocTypecodes_UpperExtremityVesselsUSDopplerLimited,
  SinusesCTLimitedWOContrast: C80DocTypecodes_SinusesCTLimitedWOContrast,
  CTWholeBody: C80DocTypecodes_CTWholeBody,
  CTWholeBodyWContrastIV: C80DocTypecodes_CTWholeBodyWContrastIV,
  LowerExtremityVesselsRightCTAngiogramWAndWOContrastIV: C80DocTypecodes_LowerExtremityVesselsRightCTAngiogramWAndWOContrastIV,
  LowerExtremityVesselsLeftCTAngiogramWAndWOContrastIV: C80DocTypecodes_LowerExtremityVesselsLeftCTAngiogramWAndWOContrastIV,
  UpperExtremityVesselsRightCTAngiogramWAndWOContrastIV: C80DocTypecodes_UpperExtremityVesselsRightCTAngiogramWAndWOContrastIV,
  OrbitAndFaceAndNeckMRIWAndWOContrastIV: C80DocTypecodes_OrbitAndFaceAndNeckMRIWAndWOContrastIV,
  ParotidGlandCTWAndWOContrastIV: C80DocTypecodes_ParotidGlandCTWAndWOContrastIV,
  UpperExtremityVesselsLeftCTAngiogramWAndWOContrastIV: C80DocTypecodes_UpperExtremityVesselsLeftCTAngiogramWAndWOContrastIV,
  PelvisCTWAndWOReducedContrastVolumeIV: C80DocTypecodes_PelvisCTWAndWOReducedContrastVolumeIV,
  InternalAuditoryCanalCTWAndWOReducedContrastVolumeIV: C80DocTypecodes_InternalAuditoryCanalCTWAndWOReducedContrastVolumeIV,
  FacialBonesAndMaxillaCTWAndWOReducedContrastVolumeIV: C80DocTypecodes_FacialBonesAndMaxillaCTWAndWOReducedContrastVolumeIV,
  HeadCTWAndWOReducedContrastVolumeIV: C80DocTypecodes_HeadCTWAndWOReducedContrastVolumeIV,
  ChestCTWAndWOReducedContrastVolumeIV: C80DocTypecodes_ChestCTWAndWOReducedContrastVolumeIV,
  AbdomenCTWAndWOReducedContrastVolumeIV: C80DocTypecodes_AbdomenCTWAndWOReducedContrastVolumeIV,
  ElbowMRIWContrastIntraarticular: C80DocTypecodes_ElbowMRIWContrastIntraarticular,
  OrbitAndFaceCTWContrastIV: C80DocTypecodes_OrbitAndFaceCTWContrastIV,
  OrbitAndFaceAndNeckMRIWContrastIV: C80DocTypecodes_OrbitAndFaceAndNeckMRIWContrastIV,
  KidneyCTWContrastIV: C80DocTypecodes_KidneyCTWContrastIV,
  BreastUnilateralMRIWContrastIV: C80DocTypecodes_BreastUnilateralMRIWContrastIV,
  LowerExtremityVesselsMRIAngiogramWContrastIV: C80DocTypecodes_LowerExtremityVesselsMRIAngiogramWContrastIV,
  InternalAuditoryCanalCTWReducedContrastVolumeIV: C80DocTypecodes_InternalAuditoryCanalCTWReducedContrastVolumeIV,
  FacialBonesAndMaxillaCTWReducedContrastVolumeIV: C80DocTypecodes_FacialBonesAndMaxillaCTWReducedContrastVolumeIV,
  ChestCTWReducedContrastVolumeIV: C80DocTypecodes_ChestCTWReducedContrastVolumeIV,
  HeadCTWReducedContrastVolumeIV: C80DocTypecodes_HeadCTWReducedContrastVolumeIV,
  PelvisCTWReducedContrastVolumeIV: C80DocTypecodes_PelvisCTWReducedContrastVolumeIV,
  AbdomenCTWReducedContrastVolumeIV: C80DocTypecodes_AbdomenCTWReducedContrastVolumeIV,
  OrbitCTWOContrast: C80DocTypecodes_OrbitCTWOContrast,
  OrbitAndFaceAndNeckMRIWOContrast: C80DocTypecodes_OrbitAndFaceAndNeckMRIWOContrast,
  BreastUnilateralMRIWOContrast: C80DocTypecodes_BreastUnilateralMRIWOContrast,
  BreastBilateralMammogramSingleView: C80DocTypecodes_BreastBilateralMammogramSingleView,
  BreastLeftMammogramSingleView: C80DocTypecodes_BreastLeftMammogramSingleView,
  BreastRightMammogramSingleView: C80DocTypecodes_BreastRightMammogramSingleView,
  BreastUnilateralMammogramSingleView: C80DocTypecodes_BreastUnilateralMammogramSingleView,
  BreastUnilateralMammogram: C80DocTypecodes_BreastUnilateralMammogram,
  SpineLumbosacralJunctionXRay: C80DocTypecodes_SpineLumbosacralJunctionXRay,
  AbdomenFluoroscopy: C80DocTypecodes_AbdomenFluoroscopy,
  BreastFFDMammogram: C80DocTypecodes_BreastFFDMammogram,
  WristRightXRayGE3Views: C80DocTypecodes_WristRightXRayGE3Views,
  ElbowLeftXRayGE3Views: C80DocTypecodes_ElbowLeftXRayGE3Views,
  ElbowRightXRayGE3Views: C80DocTypecodes_ElbowRightXRayGE3Views,
  WristLeftXRayGE3Views: C80DocTypecodes_WristLeftXRayGE3Views,
  AnkleRightXRayGE3Views: C80DocTypecodes_AnkleRightXRayGE3Views,
  ChestXRayGE2AndPAAndLateralViews: C80DocTypecodes_ChestXRayGE2AndPAAndLateralViews,
  ShoulderBilateralXRayAPAndTransthoracic: C80DocTypecodes_ShoulderBilateralXRayAPAndTransthoracic,
  BreastUnilateralMammogramDiagnostic: C80DocTypecodes_BreastUnilateralMammogramDiagnostic,
  BreastImplantBilateralMammogramDisplacement: C80DocTypecodes_BreastImplantBilateralMammogramDisplacement,
  BreastDuctMammogramDuringSurgeryWContrastIntraDuct: C80DocTypecodes_BreastDuctMammogramDuringSurgeryWContrastIntraDuct,
  BreastRightFFDMammogramScreening: C80DocTypecodes_BreastRightFFDMammogramScreening,
  BreastLeftFFDMammogramScreening: C80DocTypecodes_BreastLeftFFDMammogramScreening,
  BreastUnilateralMammogramScreening: C80DocTypecodes_BreastUnilateralMammogramScreening,
  ColonFluoroscopyWAirContrastPR: C80DocTypecodes_ColonFluoroscopyWAirContrastPR,
  MRIWholeBody: C80DocTypecodes_MRIWholeBody,
  SuperiorMesentericVesselsMRIAngiogram: C80DocTypecodes_SuperiorMesentericVesselsMRIAngiogram,
  AorticArchMRIAngiogramWAndWOContrastIV: C80DocTypecodes_AorticArchMRIAngiogramWAndWOContrastIV,
  LungScanVentilationWXe133Inhaled: C80DocTypecodes_LungScanVentilationWXe133Inhaled,
  FootVesselsUSDoppler: C80DocTypecodes_FootVesselsUSDoppler,
  LowerExtremityVeinUS: C80DocTypecodes_LowerExtremityVeinUS,
  LowerExtremityVeinBilateralUS: C80DocTypecodes_LowerExtremityVeinBilateralUS,
  CTGuidanceForAblationOfTissueOfCeliacPlexus: C80DocTypecodes_CTGuidanceForAblationOfTissueOfCeliacPlexus,
  SPECTGuidanceForBiopsyOfBone: C80DocTypecodes_SPECTGuidanceForBiopsyOfBone,
  CTGuidanceForNeedleBiopsyOfAdrenalGland: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfAdrenalGland,
  CTGuidanceForNeedleBiopsyOfBreast: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfBreast,
  USGuidanceForNeedleBiopsyOfOvary: C80DocTypecodes_USGuidanceForNeedleBiopsyOfOvary,
  USGuidanceForNeedleBiopsyOfPelvis: C80DocTypecodes_USGuidanceForNeedleBiopsyOfPelvis,
  XRayGuidanceForChangeOfPercutaneousTubeInUnspecifiedBodyRegionWContrast: C80DocTypecodes_XRayGuidanceForChangeOfPercutaneousTubeInUnspecifiedBodyRegionWContrast,
  FluoroscopyGuidanceForPercutaneousDrainageOfBiliaryDucts: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfBiliaryDucts,
  SPECTGuidanceForPlacementOfTubeInChest: C80DocTypecodes_SPECTGuidanceForPlacementOfTubeInChest,
  CerebralArteryUS: C80DocTypecodes_CerebralArteryUS,
  ArteryUS: C80DocTypecodes_ArteryUS,
  KidneyBilateralFluoroscopyWContrastAntegrade: C80DocTypecodes_KidneyBilateralFluoroscopyWContrastAntegrade,
  SkullXRayGE3Views: C80DocTypecodes_SkullXRayGE3Views,
  KneeBilateralXRayPAStandingAndWFlexion: C80DocTypecodes_KneeBilateralXRayPAStandingAndWFlexion,
  UpperExtremityVesselsBilateralUSDoppler: C80DocTypecodes_UpperExtremityVesselsBilateralUSDoppler,
  BreastImplantUnilateralMammogram: C80DocTypecodes_BreastImplantUnilateralMammogram,
  ElbowPlusRadiusPlusUlnaXRay: C80DocTypecodes_ElbowPlusRadiusPlusUlnaXRay,
  HandVesselsUSDoppler: C80DocTypecodes_HandVesselsUSDoppler,
  SPECTGuidanceForBiopsyOfSuperficialBone: C80DocTypecodes_SPECTGuidanceForBiopsyOfSuperficialBone,
  UpperExtremityVesselGraftUSDoppler: C80DocTypecodes_UpperExtremityVesselGraftUSDoppler,
  TeethXRayBitewing: C80DocTypecodes_TeethXRayBitewing,
  MammogramGuidanceForFineNeedleAspirationOfBreast: C80DocTypecodes_MammogramGuidanceForFineNeedleAspirationOfBreast,
  AortaUSDoppler: C80DocTypecodes_AortaUSDoppler,
  ElbowBilateralXRayAndRadialHeadCapitellar: C80DocTypecodes_ElbowBilateralXRayAndRadialHeadCapitellar,
  AnkleLeftXRayGE3Views: C80DocTypecodes_AnkleLeftXRayGE3Views,
  ShoulderXRayPortable: C80DocTypecodes_ShoulderXRayPortable,
  FluoroscopyGuidanceForInjectionOfSinuses: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSinuses,
  LiverCTWXe133Inhaled: C80DocTypecodes_LiverCTWXe133Inhaled,
  HeadCTDynamicWContrastIV: C80DocTypecodes_HeadCTDynamicWContrastIV,
  HeartSPECTGatedAndEjectionFractionAtRestAndWStressAndWRadionuclideIV: C80DocTypecodes_HeartSPECTGatedAndEjectionFractionAtRestAndWStressAndWRadionuclideIV,
  HeartSPECTGatedAtRestAndWTc99mSestamibiIV: C80DocTypecodes_HeartSPECTGatedAtRestAndWTc99mSestamibiIV,
  HospitalAdmissionHistoryAndPhysicalNote: C80DocTypecodes_HospitalAdmissionHistoryAndPhysicalNote,
  Consultation2ndOpinion: C80DocTypecodes_Consultation2ndOpinion,
  HospitalConsultation2ndOpinion: C80DocTypecodes_HospitalConsultation2ndOpinion,
  CounselingNote: C80DocTypecodes_CounselingNote,
  GroupCounselingNote: C80DocTypecodes_GroupCounselingNote,
  HospitalInitialAssessmentNote: C80DocTypecodes_HospitalInitialAssessmentNote,
  StudyReport: C80DocTypecodes_StudyReport,
  SummaryOfDeathNote: C80DocTypecodes_SummaryOfDeathNote,
  SupervisoryNote: C80DocTypecodes_SupervisoryNote,
  DiagnosticInterventionalStudyReportInterventionalRadiology: C80DocTypecodes_DiagnosticInterventionalStudyReportInterventionalRadiology,
  HIVTreatmentFormDocument: C80DocTypecodes_HIVTreatmentFormDocument,
  ChestCTLimitedWOContrast: C80DocTypecodes_ChestCTLimitedWOContrast,
  ChestFluoroscopyGE4Views: C80DocTypecodes_ChestFluoroscopyGE4Views,
  ChestXRayGE4AndPaAndLateralViews: C80DocTypecodes_ChestXRayGE4AndPaAndLateralViews,
  HandLeftXRayGE3Views: C80DocTypecodes_HandLeftXRayGE3Views,
  HandRightXRayGE3Views: C80DocTypecodes_HandRightXRayGE3Views,
  HipXRayDuringSurgery: C80DocTypecodes_HipXRayDuringSurgery,
  KneeLeftXRay1Or2Views: C80DocTypecodes_KneeLeftXRay1Or2Views,
  KneeLeftXRayGE4Views: C80DocTypecodes_KneeLeftXRayGE4Views,
  KneeRightXRay1Or2Views: C80DocTypecodes_KneeRightXRay1Or2Views,
  KneeRightXRayGE4Views: C80DocTypecodes_KneeRightXRayGE4Views,
  KneeRightXRayLE4Views: C80DocTypecodes_KneeRightXRayLE4Views,
  LiverSPECTBloodPool: C80DocTypecodes_LiverSPECTBloodPool,
  MandibleXRayGE4Views: C80DocTypecodes_MandibleXRayGE4Views,
  MandibleXRayLE3Views: C80DocTypecodes_MandibleXRayLE3Views,
  MastoidXRayGE3Views: C80DocTypecodes_MastoidXRayGE3Views,
  SpineLumbarXRayGE4Views: C80DocTypecodes_SpineLumbarXRayGE4Views,
  FunctionalStatusAssessmentNote: C80DocTypecodes_FunctionalStatusAssessmentNote,
  HistoryOfProceduresDocument: C80DocTypecodes_HistoryOfProceduresDocument,
  CytologyReportOfSputumCytoStain: C80DocTypecodes_CytologyReportOfSputumCytoStain,
  CytologyReportOfBreastFineNeedleAspirateCytoStain: C80DocTypecodes_CytologyReportOfBreastFineNeedleAspirateCytoStain,
  CytologyReportOfNippleDischargeCytoStain: C80DocTypecodes_CytologyReportOfNippleDischargeCytoStain,
  CytologyReportOfBodyFluidCytoStain: C80DocTypecodes_CytologyReportOfBodyFluidCytoStain,
  CytologyReportOfThyroidFineNeedleAspirateCytoStain: C80DocTypecodes_CytologyReportOfThyroidFineNeedleAspirateCytoStain,
  CytologyReportOfUrineCytoStain: C80DocTypecodes_CytologyReportOfUrineCytoStain,
  CytologyReportOfUnspecifiedSpecimenCytoStain: C80DocTypecodes_CytologyReportOfUnspecifiedSpecimenCytoStain,
  CytologyReportOfCervicalOrVaginalSmearOrScrapingCytoStainThinPrep: C80DocTypecodes_CytologyReportOfCervicalOrVaginalSmearOrScrapingCytoStainThinPrep,
  CytologyReportOfCervicalOrVaginalSmearOrScrapingCytoStain: C80DocTypecodes_CytologyReportOfCervicalOrVaginalSmearOrScrapingCytoStain,
  CytologyReportOfTissueOtherStain: C80DocTypecodes_CytologyReportOfTissueOtherStain,
  CytologyReportOfBreastDuctalLavageCytoStain: C80DocTypecodes_CytologyReportOfBreastDuctalLavageCytoStain,
  MastoidBilateralXRay1Or2Views: C80DocTypecodes_MastoidBilateralXRay1Or2Views,
  PelvisAndSpineLumbarXRay: C80DocTypecodes_PelvisAndSpineLumbarXRay,
  SpineCTWContrastIT: C80DocTypecodes_SpineCTWContrastIT,
  LowerExtremityArteriesLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_LowerExtremityArteriesLeftFluoroscopicAngiogramWContrastIA,
  LowerExtremityArteriesRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_LowerExtremityArteriesRightFluoroscopicAngiogramWContrastIA,
  CalcaneusBilateralXRay2Views: C80DocTypecodes_CalcaneusBilateralXRay2Views,
  USGuidanceForAspirationOfKidney: C80DocTypecodes_USGuidanceForAspirationOfKidney,
  FluoroscopyGuidanceForInjectionOfSalivaryGlandBilateral: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSalivaryGlandBilateral,
  SpineLumbarMRIWContrastIT: C80DocTypecodes_SpineLumbarMRIWContrastIT,
  SpineThoracicMRIWContrastIT: C80DocTypecodes_SpineThoracicMRIWContrastIT,
  SkullBaseMRIWContrastIV: C80DocTypecodes_SkullBaseMRIWContrastIV,
  SpineThoracicMRIWAndWOContrastIT: C80DocTypecodes_SpineThoracicMRIWAndWOContrastIT,
  SpineCTWAndWOContrastIT: C80DocTypecodes_SpineCTWAndWOContrastIT,
  NasopharynxCTWAndWOContrastIV: C80DocTypecodes_NasopharynxCTWAndWOContrastIV,
  BrainTemporalMRIWContrastIV: C80DocTypecodes_BrainTemporalMRIWContrastIV,
  LarynxMRIWOContrast: C80DocTypecodes_LarynxMRIWOContrast,
  NasopharynxCTWContrastIV: C80DocTypecodes_NasopharynxCTWContrastIV,
  SpineCervicalMRIWContrastIT: C80DocTypecodes_SpineCervicalMRIWContrastIT,
  UpperExtremityArteryUS: C80DocTypecodes_UpperExtremityArteryUS,
  OrbitCTWContrastIV: C80DocTypecodes_OrbitCTWContrastIV,
  SpineCervicalMRIWAndWOContrastIT: C80DocTypecodes_SpineCervicalMRIWAndWOContrastIT,
  OrbitCTWAndWOContrastIV: C80DocTypecodes_OrbitCTWAndWOContrastIV,
  SpineLumbarMRIWAndWOContrastIT: C80DocTypecodes_SpineLumbarMRIWAndWOContrastIT,
  BrainTemporalMRIWOContrast: C80DocTypecodes_BrainTemporalMRIWOContrast,
  ClavicleRightMRIWAndWOContrastIV: C80DocTypecodes_ClavicleRightMRIWAndWOContrastIV,
  ClavicleLeftMRIWAndWOContrastIV: C80DocTypecodes_ClavicleLeftMRIWAndWOContrastIV,
  ClavicleRightMRIWContrastIV: C80DocTypecodes_ClavicleRightMRIWContrastIV,
  ClavicleLeftMRIWContrastIV: C80DocTypecodes_ClavicleLeftMRIWContrastIV,
  ClavicleRightMRIWOContrast: C80DocTypecodes_ClavicleRightMRIWOContrast,
  ClavicleLeftMRIWOContrast: C80DocTypecodes_ClavicleLeftMRIWOContrast,
  UnspecifiedBodyRegionMRILimited: C80DocTypecodes_UnspecifiedBodyRegionMRILimited,
  NeckMRILimited: C80DocTypecodes_NeckMRILimited,
  KneeLeftXRayAPSingleView: C80DocTypecodes_KneeLeftXRayAPSingleView,
  KneeRightXRayAPSingleView: C80DocTypecodes_KneeRightXRayAPSingleView,
  TracheaFluoroscopy: C80DocTypecodes_TracheaFluoroscopy,
  LarynxFluoroscopy: C80DocTypecodes_LarynxFluoroscopy,
  SkullXRayLimited: C80DocTypecodes_SkullXRayLimited,
  SacroiliacJointXRay1Or2Views: C80DocTypecodes_SacroiliacJointXRay1Or2Views,
  RibsBilateralAndChestXRay2ViewsAndPAChest: C80DocTypecodes_RibsBilateralAndChestXRay2ViewsAndPAChest,
  SpineLumbarXRay2Or3Views: C80DocTypecodes_SpineLumbarXRay2Or3Views,
  MastoidLeftXRay3Views: C80DocTypecodes_MastoidLeftXRay3Views,
  MastoidRightXRay3Views: C80DocTypecodes_MastoidRightXRay3Views,
  SpineThoracicXRay3ViewsAndSwimmers: C80DocTypecodes_SpineThoracicXRay3ViewsAndSwimmers,
  SpineLumbarAndSacrumXRay4Views: C80DocTypecodes_SpineLumbarAndSacrumXRay4Views,
  HandBilateralXRayAPAndLateral: C80DocTypecodes_HandBilateralXRayAPAndLateral,
  BreastImplantBilateralMammogramDiagnostic: C80DocTypecodes_BreastImplantBilateralMammogramDiagnostic,
  FootRightXRayGE3Views: C80DocTypecodes_FootRightXRayGE3Views,
  FootLeftXRayGE3Views: C80DocTypecodes_FootLeftXRayGE3Views,
  FootBilateralXRayGE3Views: C80DocTypecodes_FootBilateralXRayGE3Views,
  FacialBonesXRayGE3Views: C80DocTypecodes_FacialBonesXRayGE3Views,
  AnkleBilateralXRayGE3Views: C80DocTypecodes_AnkleBilateralXRayGE3Views,
  ElbowBilateralXRayGE3Views: C80DocTypecodes_ElbowBilateralXRayGE3Views,
  SternoclavicularJointsXRayGE3Views: C80DocTypecodes_SternoclavicularJointsXRayGE3Views,
  WristBilateralXRayGE3Views: C80DocTypecodes_WristBilateralXRayGE3Views,
  RibsRightAndChestXRayGE3AndPAChestViews: C80DocTypecodes_RibsRightAndChestXRayGE3AndPAChestViews,
  RibsBilateralAndChestXRayGE3AndPAChestViews: C80DocTypecodes_RibsBilateralAndChestXRayGE3AndPAChestViews,
  RibsLeftAndChestXRayGE3AndPAChestViews: C80DocTypecodes_RibsLeftAndChestXRayGE3AndPAChestViews,
  SkullXRayGE4Views: C80DocTypecodes_SkullXRayGE4Views,
  MastoidRightXRay1Or2Views: C80DocTypecodes_MastoidRightXRay1Or2Views,
  MastoidLeftXRay1Or2Views: C80DocTypecodes_MastoidLeftXRay1Or2Views,
  TemporomandibularJointRightXRayOpenAndClosedMouth: C80DocTypecodes_TemporomandibularJointRightXRayOpenAndClosedMouth,
  TemporomandibularJointLeftXRayOpenAndClosedMouth: C80DocTypecodes_TemporomandibularJointLeftXRayOpenAndClosedMouth,
  BreastImplantBilateralMammogramScreening: C80DocTypecodes_BreastImplantBilateralMammogramScreening,
  SkullBaseMRIWOContrast: C80DocTypecodes_SkullBaseMRIWOContrast,
  UpperExtremityVeinRightUS: C80DocTypecodes_UpperExtremityVeinRightUS,
  UpperExtremityVeinLeftUS: C80DocTypecodes_UpperExtremityVeinLeftUS,
  UpperExtremityVeinBilateralUS: C80DocTypecodes_UpperExtremityVeinBilateralUS,
  LowerExtremityVeinRightUS: C80DocTypecodes_LowerExtremityVeinRightUS,
  LowerExtremityVeinLeftUS: C80DocTypecodes_LowerExtremityVeinLeftUS,
  LowerExtremityArteryUS: C80DocTypecodes_LowerExtremityArteryUS,
  BrainTemporalMRIWAndWOContrastIV: C80DocTypecodes_BrainTemporalMRIWAndWOContrastIV,
  SkullBaseXRaySingleView: C80DocTypecodes_SkullBaseXRaySingleView,
  SubmandibularGlandRightFluoroscopyWContrastIntraSalivaryDuct: C80DocTypecodes_SubmandibularGlandRightFluoroscopyWContrastIntraSalivaryDuct,
  SkullBaseXRay: C80DocTypecodes_SkullBaseXRay,
  SubmandibularGlandBilateralFluoroscopyWContrastIntraSalivaryDuct: C80DocTypecodes_SubmandibularGlandBilateralFluoroscopyWContrastIntraSalivaryDuct,
  TemporomandibularJointUnilateralXRayOpenAndClosedMouth: C80DocTypecodes_TemporomandibularJointUnilateralXRayOpenAndClosedMouth,
  MammogramGuidanceForLocalizationOfBreast: C80DocTypecodes_MammogramGuidanceForLocalizationOfBreast,
  MammogramGuidanceForSentinelLymphNodeInjectionOfBreastLeft: C80DocTypecodes_MammogramGuidanceForSentinelLymphNodeInjectionOfBreastLeft,
  WristAndHandXRay3Views: C80DocTypecodes_WristAndHandXRay3Views,
  WristBilateralAndHandBilateralXRay3Views: C80DocTypecodes_WristBilateralAndHandBilateralXRay3Views,
  MammogramGuidanceForSentinelLymphNodeInjectionOfBreastRight: C80DocTypecodes_MammogramGuidanceForSentinelLymphNodeInjectionOfBreastRight,
  MammogramGuidanceForSentinelLymphNodeInjectionOfBreast: C80DocTypecodes_MammogramGuidanceForSentinelLymphNodeInjectionOfBreast,
  ScrotumAndTesticleUSDoppler: C80DocTypecodes_ScrotumAndTesticleUSDoppler,
  AbdomenRetroperitoneumCTWAndWOContrastIV: C80DocTypecodes_AbdomenRetroperitoneumCTWAndWOContrastIV,
  MandibleLeftXRay: C80DocTypecodes_MandibleLeftXRay,
  SacroiliacJointBilateralXRayGE3Views: C80DocTypecodes_SacroiliacJointBilateralXRayGE3Views,
  OrbitBilateralXRayGE4Views: C80DocTypecodes_OrbitBilateralXRayGE4Views,
  SpineXRayOblique: C80DocTypecodes_SpineXRayOblique,
  SpineThoracicXRayOblique: C80DocTypecodes_SpineThoracicXRayOblique,
  SummaryPurposeCCDDocument: C80DocTypecodes_SummaryPurposeCCDDocument,
  AllergiesAndAdverseReactionsDocument: C80DocTypecodes_AllergiesAndAdverseReactionsDocument,
  InformationSource: C80DocTypecodes_InformationSource,
  AnnotationCommentNarrative: C80DocTypecodes_AnnotationCommentNarrative,
  PaymentSourcesDocument: C80DocTypecodes_PaymentSourcesDocument,
  FDAPackageInsertStructuredProductLabellingIndexingDataElementsSection: C80DocTypecodes_FDAPackageInsertStructuredProductLabellingIndexingDataElementsSection,
  FDAPackageInsertStructuredProductLabellingListingDataElementsSection: C80DocTypecodes_FDAPackageInsertStructuredProductLabellingListingDataElementsSection,
  BoneMarrowAspirationReport: C80DocTypecodes_BoneMarrowAspirationReport,
  UnspecifiedBodyRegionScan: C80DocTypecodes_UnspecifiedBodyRegionScan,
  FDAPackageInsertMicrobiologySection: C80DocTypecodes_FDAPackageInsertMicrobiologySection,
  UnspecifiedBodyRegionMRIWContrastIV: C80DocTypecodes_UnspecifiedBodyRegionMRIWContrastIV,
  BreastDuctRightMammogramSingleViewWContrastIntraDuct: C80DocTypecodes_BreastDuctRightMammogramSingleViewWContrastIntraDuct,
  BreastDuctLeftMammogramSingleViewWContrastIntraDuct: C80DocTypecodes_BreastDuctLeftMammogramSingleViewWContrastIntraDuct,
  FemoralArteryFluoroscopicAngiogramRunoffWAndWOContrastIA: C80DocTypecodes_FemoralArteryFluoroscopicAngiogramRunoffWAndWOContrastIA,
  UnspecifiedBodyRegionFluoroscopy: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopy,
  ThoracicSpineVesselsMRIAngiogram: C80DocTypecodes_ThoracicSpineVesselsMRIAngiogram,
  HeartSPECTAtRestAndWTc99mSestamibiIV: C80DocTypecodes_HeartSPECTAtRestAndWTc99mSestamibiIV,
  HeartSPECTPerfusionWAdenosineAndWTc99mSestamibiIV: C80DocTypecodes_HeartSPECTPerfusionWAdenosineAndWTc99mSestamibiIV,
  HeartSPECTPerfusionAtRestAndWStressAndWTl201IVAndWTc99mSestamibiIV: C80DocTypecodes_HeartSPECTPerfusionAtRestAndWStressAndWTl201IVAndWTc99mSestamibiIV,
  HeartSPECTPerfusionAndWallMotionAtRestAndWStressAndWTl201IVAndWTc99mSestamibiIV: C80DocTypecodes_HeartSPECTPerfusionAndWallMotionAtRestAndWStressAndWTl201IVAndWTc99mSestamibiIV,
  AnkleBilateralXRayGE6Views: C80DocTypecodes_AnkleBilateralXRayGE6Views,
  ScanLimitedWI131MIBGIV: C80DocTypecodes_ScanLimitedWI131MIBGIV,
  CytologyReportOfBronchoalveolarLavageCytoStain: C80DocTypecodes_CytologyReportOfBronchoalveolarLavageCytoStain,
  VAL18HydroxydeoxycorticosteroneMolesVolumeInSerumOrPlasma: C80DocTypecodes_VAL18HydroxydeoxycorticosteroneMolesVolumeInSerumOrPlasma,
  LowerExtremityBilateralCTWContrastIV: C80DocTypecodes_LowerExtremityBilateralCTWContrastIV,
  CytologyReportOfBronchialBrushCytoStain: C80DocTypecodes_CytologyReportOfBronchialBrushCytoStain,
  KneeBilateralXRayAndAPViewStanding: C80DocTypecodes_KneeBilateralXRayAndAPViewStanding,
  WristRightAndHandRightXRay: C80DocTypecodes_WristRightAndHandRightXRay,
  BreastScanWTl201IV: C80DocTypecodes_BreastScanWTl201IV,
  FluoroscopicAngiogramGuidanceForPlacementOfTransjugularIntrahepaticPortosystemicShuntInPortalVeinAndHepaticVein: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfTransjugularIntrahepaticPortosystemicShuntInPortalVeinAndHepaticVein,
  WristLeftAndHandLeftXRay: C80DocTypecodes_WristLeftAndHandLeftXRay,
  AnkleRightAndFootRightXRay: C80DocTypecodes_AnkleRightAndFootRightXRay,
  AnkleLeftAndFootLeftXRay: C80DocTypecodes_AnkleLeftAndFootLeftXRay,
  OutpatientConsultNote: C80DocTypecodes_OutpatientConsultNote,
  EmergencyDepartmentConsultNote: C80DocTypecodes_EmergencyDepartmentConsultNote,
  AssessmentPlusPlanNote: C80DocTypecodes_AssessmentPlusPlanNote,
  AssessmentNote: C80DocTypecodes_AssessmentNote,
  AdmissionHistoryAndPhysicalNote: C80DocTypecodes_AdmissionHistoryAndPhysicalNote,
  PhysicalFindingsOfHeadAndEarsAndEyesAndNoseAndThroat: C80DocTypecodes_PhysicalFindingsOfHeadAndEarsAndEyesAndNoseAndThroat,
  AdministrativeNote: C80DocTypecodes_AdministrativeNote,
  Letter: C80DocTypecodes_Letter,
  LongTermCareFacilityConsultNote: C80DocTypecodes_LongTermCareFacilityConsultNote,
  PatientNote: C80DocTypecodes_PatientNote,
  HealthcareAssociatedInfectionReportDocument: C80DocTypecodes_HealthcareAssociatedInfectionReportDocument,
  RiskFactorsDocument: C80DocTypecodes_RiskFactorsDocument,
  DetailsDocument: C80DocTypecodes_DetailsDocument,
  PopulationSummaryNote: C80DocTypecodes_PopulationSummaryNote,
  FDAProductLabelBackPanelOfPackage: C80DocTypecodes_FDAProductLabelBackPanelOfPackage,
  FDAProductLabelSidePanelOfPackageRight: C80DocTypecodes_FDAProductLabelSidePanelOfPackageRight,
  FDAProductLabelSidePanelOfPackageLeft: C80DocTypecodes_FDAProductLabelSidePanelOfPackageLeft,
  FDAProductLabelSidePanelOfPackage: C80DocTypecodes_FDAProductLabelSidePanelOfPackage,
  FDAProductLabelPrincipalDisplayPanelOfPackage: C80DocTypecodes_FDAProductLabelPrincipalDisplayPanelOfPackage,
  FDAProductLabelTopPanelOfPackage: C80DocTypecodes_FDAProductLabelTopPanelOfPackage,
  FDAProductLabelBottomPanelOfPackage: C80DocTypecodes_FDAProductLabelBottomPanelOfPackage,
  FDAProductLabelFlapPanelOfPackage: C80DocTypecodes_FDAProductLabelFlapPanelOfPackage,
  PharmacogeneticAnalysisReportInBloodOrTissueDocumentByMolecularGeneticsMethod: C80DocTypecodes_PharmacogeneticAnalysisReportInBloodOrTissueDocumentByMolecularGeneticsMethod,
  GeneticAnalysisSummaryReportInBloodOrTissueDocumentByMolecularGeneticsMethod: C80DocTypecodes_GeneticAnalysisSummaryReportInBloodOrTissueDocumentByMolecularGeneticsMethod,
  AbortionConsent: C80DocTypecodes_AbortionConsent,
  HysterectomyConsent: C80DocTypecodes_HysterectomyConsent,
  SterilizationConsent: C80DocTypecodes_SterilizationConsent,
  ExplanationOfBenefits: C80DocTypecodes_ExplanationOfBenefits,
  ExplanationOfBenefitsToSubscriber: C80DocTypecodes_ExplanationOfBenefitsToSubscriber,
  AppealDenialLetter: C80DocTypecodes_AppealDenialLetter,
  GeneralCorrespondence: C80DocTypecodes_GeneralCorrespondence,
  PayerLetter: C80DocTypecodes_PayerLetter,
  HomeHealthClaims: C80DocTypecodes_HomeHealthClaims,
  HomeHealthPriorAuthorization: C80DocTypecodes_HomeHealthPriorAuthorization,
  MemberIDCardCopy: C80DocTypecodes_MemberIDCardCopy,
  SubscriberInformationIncludingRetroactiveAndPresumptiveEligibility: C80DocTypecodes_SubscriberInformationIncludingRetroactiveAndPresumptiveEligibility,
  SkilledNursingFacilitySNFRecord: C80DocTypecodes_SkilledNursingFacilitySNFRecord,
  DentalXRaysAndOtherImagesNotDICOM: C80DocTypecodes_DentalXRaysAndOtherImagesNotDICOM,
  BloodGlucoseMonitors: C80DocTypecodes_BloodGlucoseMonitors,
  ContinuousPositiveAirwayPressureCPAP: C80DocTypecodes_ContinuousPositiveAirwayPressureCPAP,
  EnteralNutrition: C80DocTypecodes_EnteralNutrition,
  ExternalInfusionPump: C80DocTypecodes_ExternalInfusionPump,
  GaitTrainers: C80DocTypecodes_GaitTrainers,
  HospitalBeds: C80DocTypecodes_HospitalBeds,
  ImmunosuppressiveDrugs: C80DocTypecodes_ImmunosuppressiveDrugs,
  LymphedemaPumps: C80DocTypecodes_LymphedemaPumps,
  ManualWheelchair: C80DocTypecodes_ManualWheelchair,
  MotorizedWheelchair: C80DocTypecodes_MotorizedWheelchair,
  OrthoticsProsthetics: C80DocTypecodes_OrthoticsProsthetics,
  OsteogenesisStimulators: C80DocTypecodes_OsteogenesisStimulators,
  Oxygen: C80DocTypecodes_Oxygen,
  Parenteral: C80DocTypecodes_Parenteral,
  PowerOperatedVehicles: C80DocTypecodes_PowerOperatedVehicles,
  RepairOfDurableMedicalEquipment: C80DocTypecodes_RepairOfDurableMedicalEquipment,
  SeatLiftMechanism: C80DocTypecodes_SeatLiftMechanism,
  SeatingSystems: C80DocTypecodes_SeatingSystems,
  SpeechGeneratingDevice: C80DocTypecodes_SpeechGeneratingDevice,
  StandersStandingFrames: C80DocTypecodes_StandersStandingFrames,
  SupportSurfaces: C80DocTypecodes_SupportSurfaces,
  TranscutaneousElectricalNeuralStimulationTENS: C80DocTypecodes_TranscutaneousElectricalNeuralStimulationTENS,
  PrescriptionForDurableMedicalEquipmentDME: C80DocTypecodes_PrescriptionForDurableMedicalEquipmentDME,
  FirstReportOfInjury: C80DocTypecodes_FirstReportOfInjury,
  AutomobileLiability: C80DocTypecodes_AutomobileLiability,
  NoticeOfDischargeMedicareAppealRightsNODMARForm: C80DocTypecodes_NoticeOfDischargeMedicareAppealRightsNODMARForm,
  PastFilingLimitJustification: C80DocTypecodes_PastFilingLimitJustification,
  PropertyAndCasualtyStateMandatedForms: C80DocTypecodes_PropertyAndCasualtyStateMandatedForms,
  TaxIDNumberIRSFormW9: C80DocTypecodes_TaxIDNumberIRSFormW9,
  WorkersCompensation: C80DocTypecodes_WorkersCompensation,
  EmployeeAssistanceProgram: C80DocTypecodes_EmployeeAssistanceProgram,
  NonEmergencyTransportation: C80DocTypecodes_NonEmergencyTransportation,
  VisionAttachment: C80DocTypecodes_VisionAttachment,
  PurchaseInvoice: C80DocTypecodes_PurchaseInvoice,
  PulmonaryTherapyServiceAttachment: C80DocTypecodes_PulmonaryTherapyServiceAttachment,
  CTGuidanceForReplacementOfPercutaneousDrainageTubeInAbdomen: C80DocTypecodes_CTGuidanceForReplacementOfPercutaneousDrainageTubeInAbdomen,
  CTGuidanceForReplacementOfPercutaneousDrainageTubeInPelvis: C80DocTypecodes_CTGuidanceForReplacementOfPercutaneousDrainageTubeInPelvis,
  ChargeTicketOrEncounterForm: C80DocTypecodes_ChargeTicketOrEncounterForm,
  AdvancedBeneficiaryNotice: C80DocTypecodes_AdvancedBeneficiaryNotice,
  NoticeOfPrivacyPracticesReceipt: C80DocTypecodes_NoticeOfPrivacyPracticesReceipt,
  DriverLicenseImage: C80DocTypecodes_DriverLicenseImage,
  NonMedicalServices: C80DocTypecodes_NonMedicalServices,
  EligibilityAcknowledgement: C80DocTypecodes_EligibilityAcknowledgement,
  VAL11DeoxycorticosteroneMassVolumeInDriedBloodSpot: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInDriedBloodSpot,
  VAL11DeoxycorticosteroneMolesVolumeInDriedBloodSpot: C80DocTypecodes_VAL11DeoxycorticosteroneMolesVolumeInDriedBloodSpot,
  PersonalHealthMonitoringReportDocument: C80DocTypecodes_PersonalHealthMonitoringReportDocument,
  EmergencyDepartmentTriageNote: C80DocTypecodes_EmergencyDepartmentTriageNote,
  ChemotherapyEffectivenessPanelIdentifierBloodOrTissue: C80DocTypecodes_ChemotherapyEffectivenessPanelIdentifierBloodOrTissue,
  FDAPackageInsertUserSafetyWarningsSection: C80DocTypecodes_FDAPackageInsertUserSafetyWarningsSection,
  AddendumDocument: C80DocTypecodes_AddendumDocument,
  ClinicalPresentationDocument: C80DocTypecodes_ClinicalPresentationDocument,
  ComplicationsDocument: C80DocTypecodes_ComplicationsDocument,
  ConclusionsDocument: C80DocTypecodes_ConclusionsDocument,
  CurrentImagingProcedureDescriptionsDocument: C80DocTypecodes_CurrentImagingProcedureDescriptionsDocument,
  DocumentSummary: C80DocTypecodes_DocumentSummary,
  KeyImagesDocumentRadiology: C80DocTypecodes_KeyImagesDocumentRadiology,
  PriorImagingProcedureDescriptionsDocument: C80DocTypecodes_PriorImagingProcedureDescriptionsDocument,
  RequestedImagingStudiesInformationDocument: C80DocTypecodes_RequestedImagingStudiesInformationDocument,
  SurgicalOperationNoteImplantsNarrative: C80DocTypecodes_SurgicalOperationNoteImplantsNarrative,
  QualityReportingDocumentArchitectureIncidenceReportDocument: C80DocTypecodes_QualityReportingDocumentArchitectureIncidenceReportDocument,
  QualityReportingDocumentArchitecturePatientListReportPopulationDocument: C80DocTypecodes_QualityReportingDocumentArchitecturePatientListReportPopulationDocument,
  QualityReportingDocumentArchitectureCalculatedSummaryReportPopulationDocument: C80DocTypecodes_QualityReportingDocumentArchitectureCalculatedSummaryReportPopulationDocument,
  MeasureSetDocument: C80DocTypecodes_MeasureSetDocument,
  MeasureDocument: C80DocTypecodes_MeasureDocument,
  ReportingParametersDocument: C80DocTypecodes_ReportingParametersDocument,
  PatientDataDocument: C80DocTypecodes_PatientDataDocument,
  CytogeneticsStudy: C80DocTypecodes_CytogeneticsStudy,
  ImmuneStainStudy: C80DocTypecodes_ImmuneStainStudy,
  ImmunophenotypingStudy: C80DocTypecodes_ImmunophenotypingStudy,
  PatientSafetyReportEventDocument: C80DocTypecodes_PatientSafetyReportEventDocument,
  PublicHealthCaseReportDocument: C80DocTypecodes_PublicHealthCaseReportDocument,
  VAL11DeoxycorticosteroneMolesTimeIn24HourUrine: C80DocTypecodes_VAL11DeoxycorticosteroneMolesTimeIn24HourUrine,
  HealthcareCommunicationDocument: C80DocTypecodes_HealthcareCommunicationDocument,
  MedicationSummaryDocument: C80DocTypecodes_MedicationSummaryDocument,
  AppointmentSummaryDocument: C80DocTypecodes_AppointmentSummaryDocument,
  PlanOfCareNote: C80DocTypecodes_PlanOfCareNote,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasmaBaseline: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasmaBaseline,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma30MinutesPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma30MinutesPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma1stSpecimenPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma1stSpecimenPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma2ndSpecimenPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma2ndSpecimenPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma3rdSpecimenPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma3rdSpecimenPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma4thSpecimenPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma4thSpecimenPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasmaPreXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasmaPreXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma15MinutesPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma15MinutesPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma20MinutesPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma20MinutesPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma40MinutesPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma40MinutesPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma15HoursPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma15HoursPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma2HoursPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma2HoursPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma25HoursPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma25HoursPostXXXChallenge,
  PrivacyPolicyAcknowledgmentDocument: C80DocTypecodes_PrivacyPolicyAcknowledgmentDocument,
  PrivacyPolicyOrganizationDocument: C80DocTypecodes_PrivacyPolicyOrganizationDocument,
  HealthQualityMeasureDocument: C80DocTypecodes_HealthQualityMeasureDocument,
  DataCriteriaNarrative: C80DocTypecodes_DataCriteriaNarrative,
  PopulationCriteriaNarrative: C80DocTypecodes_PopulationCriteriaNarrative,
  MeasureObservationsNarrative: C80DocTypecodes_MeasureObservationsNarrative,
  NurseEmergencyDepartmentNote: C80DocTypecodes_NurseEmergencyDepartmentNote,
  NurseEmergencyDepartmentTriagePlusCareNote: C80DocTypecodes_NurseEmergencyDepartmentTriagePlusCareNote,
  AntepartumSummaryNote: C80DocTypecodes_AntepartumSummaryNote,
  LaborAndDeliveryAdmissionHistoryAndPhysicalNote: C80DocTypecodes_LaborAndDeliveryAdmissionHistoryAndPhysicalNote,
  LaborAndDeliverySummaryNote: C80DocTypecodes_LaborAndDeliverySummaryNote,
  MaternalDischargeSummaryNote: C80DocTypecodes_MaternalDischargeSummaryNote,
  PregnancyVisitSummaryNoteNarrative: C80DocTypecodes_PregnancyVisitSummaryNoteNarrative,
  FullNewbornScreeningSummaryReportForDisplayOrPrinting: C80DocTypecodes_FullNewbornScreeningSummaryReportForDisplayOrPrinting,
  ReferralNote: C80DocTypecodes_ReferralNote,
  DentistryReferralNote: C80DocTypecodes_DentistryReferralNote,
  DermatologyReferralNote: C80DocTypecodes_DermatologyReferralNote,
  DiabetologyReferralNote: C80DocTypecodes_DiabetologyReferralNote,
  EndocrinologyReferralNote: C80DocTypecodes_EndocrinologyReferralNote,
  GastroenterologyReferralNote: C80DocTypecodes_GastroenterologyReferralNote,
  GeneralMedicineReferralNote: C80DocTypecodes_GeneralMedicineReferralNote,
  InfectiousDiseaseReferralNote: C80DocTypecodes_InfectiousDiseaseReferralNote,
  KinesiotherapyReferralNote: C80DocTypecodes_KinesiotherapyReferralNote,
  MentalHealthReferralNote: C80DocTypecodes_MentalHealthReferralNote,
  NephrologyReferralNote: C80DocTypecodes_NephrologyReferralNote,
  NeurologyReferralNote: C80DocTypecodes_NeurologyReferralNote,
  NeurologicalSurgeryReferralNote: C80DocTypecodes_NeurologicalSurgeryReferralNote,
  OccupationalMedicineReferralNote: C80DocTypecodes_OccupationalMedicineReferralNote,
  OccupationalTherapyReferralNote: C80DocTypecodes_OccupationalTherapyReferralNote,
  OncologyReferralNote: C80DocTypecodes_OncologyReferralNote,
  OphthalmologyReferralNote: C80DocTypecodes_OphthalmologyReferralNote,
  OptometryReferralNote: C80DocTypecodes_OptometryReferralNote,
  PharmacyReferralNote: C80DocTypecodes_PharmacyReferralNote,
  PhysicalMedicineAndRehabilitationReferralNote: C80DocTypecodes_PhysicalMedicineAndRehabilitationReferralNote,
  PhysicalTherapyReferralNote: C80DocTypecodes_PhysicalTherapyReferralNote,
  PlasticSurgeryReferralNote: C80DocTypecodes_PlasticSurgeryReferralNote,
  PodiatryReferralNote: C80DocTypecodes_PodiatryReferralNote,
  PsychiatryReferralNote: C80DocTypecodes_PsychiatryReferralNote,
  PsychologyReferralNote: C80DocTypecodes_PsychologyReferralNote,
  RadiationOncologyReferralNote: C80DocTypecodes_RadiationOncologyReferralNote,
  RecreationalTherapyReferralNote: C80DocTypecodes_RecreationalTherapyReferralNote,
  RespiratoryTherapyReferralNote: C80DocTypecodes_RespiratoryTherapyReferralNote,
  RheumatologyReferralNote: C80DocTypecodes_RheumatologyReferralNote,
  SocialWorkReferralNote: C80DocTypecodes_SocialWorkReferralNote,
  SpeechLanguagePathologyReferralNote: C80DocTypecodes_SpeechLanguagePathologyReferralNote,
  SurgeryReferralNote: C80DocTypecodes_SurgeryReferralNote,
  ThoracicSurgeryReferralNote: C80DocTypecodes_ThoracicSurgeryReferralNote,
  UrologyReferralNote: C80DocTypecodes_UrologyReferralNote,
  VascularSurgeryReferralNote: C80DocTypecodes_VascularSurgeryReferralNote,
  CardiologyReferralNote: C80DocTypecodes_CardiologyReferralNote,
  GeriatricMedicineReferralNote: C80DocTypecodes_GeriatricMedicineReferralNote,
  HematologyPlusMedicalOncologyReferralNote: C80DocTypecodes_HematologyPlusMedicalOncologyReferralNote,
  NutritionAndDieteticsReferralNote: C80DocTypecodes_NutritionAndDieteticsReferralNote,
  OralAndMaxillofacialSurgeryReferralNote: C80DocTypecodes_OralAndMaxillofacialSurgeryReferralNote,
  OrthopaedicSurgeryReferralNote: C80DocTypecodes_OrthopaedicSurgeryReferralNote,
  OtolaryngologyReferralNote: C80DocTypecodes_OtolaryngologyReferralNote,
  PulmonaryReferralNote: C80DocTypecodes_PulmonaryReferralNote,
  CriticalCareMedicineReferralNote: C80DocTypecodes_CriticalCareMedicineReferralNote,
  ObstetricsAndGynecologyReferralNote: C80DocTypecodes_ObstetricsAndGynecologyReferralNote,
  VAL11DeoxycorticosteroneMolesVolumeInSerumOrPlasmaPre250UgCorticotropin: C80DocTypecodes_VAL11DeoxycorticosteroneMolesVolumeInSerumOrPlasmaPre250UgCorticotropin,
  VAL11DeoxycorticosteroneMolesVolumeInSerumOrPlasma30MinutesPost250UgCorticotropin: C80DocTypecodes_VAL11DeoxycorticosteroneMolesVolumeInSerumOrPlasma30MinutesPost250UgCorticotropin,
  VAL11DeoxycorticosteroneMolesVolumeInSerumOrPlasma1HourPost250UgCorticotropin: C80DocTypecodes_VAL11DeoxycorticosteroneMolesVolumeInSerumOrPlasma1HourPost250UgCorticotropin,
  VAL18HydroxydeoxycorticosteroneMolesVolumeInSerumOrPlasmaPreDoseCorticotropin: C80DocTypecodes_VAL18HydroxydeoxycorticosteroneMolesVolumeInSerumOrPlasmaPreDoseCorticotropin,
  VAL18HydroxydeoxycorticosteroneMolesVolumeInSerumOrPlasma30MinutesPostDoseCorticotropin: C80DocTypecodes_VAL18HydroxydeoxycorticosteroneMolesVolumeInSerumOrPlasma30MinutesPostDoseCorticotropin,
  VAL18HydroxydeoxycorticosteroneMolesVolumeInSerumOrPlasma1HourPostDoseCorticotropin: C80DocTypecodes_VAL18HydroxydeoxycorticosteroneMolesVolumeInSerumOrPlasma1HourPostDoseCorticotropin,
  VAL21DeoxycorticosteroneMolesVolumeInSerumOrPlasmaPreDoseCorticotropin: C80DocTypecodes_VAL21DeoxycorticosteroneMolesVolumeInSerumOrPlasmaPreDoseCorticotropin,
  VAL21DeoxycorticosteroneMolesVolumeInSerumOrPlasma30MinutesPostDoseCorticotropin: C80DocTypecodes_VAL21DeoxycorticosteroneMolesVolumeInSerumOrPlasma30MinutesPostDoseCorticotropin,
  VAL21DeoxycorticosteroneMolesVolumeInSerumOrPlasma1HourPostDoseCorticotropin: C80DocTypecodes_VAL21DeoxycorticosteroneMolesVolumeInSerumOrPlasma1HourPostDoseCorticotropin,
  LungPET: C80DocTypecodes_LungPET,
  EsophagusPET: C80DocTypecodes_EsophagusPET,
  CoPaymentAmountNarrative: C80DocTypecodes_CoPaymentAmountNarrative,
  ReasonForCoPaymentExemptionNarrative: C80DocTypecodes_ReasonForCoPaymentExemptionNarrative,
  PrescriptionListDocument: C80DocTypecodes_PrescriptionListDocument,
  PrescriptionForMedicalEquipmentOrProductDocument: C80DocTypecodes_PrescriptionForMedicalEquipmentOrProductDocument,
  AdmissionRequestDocument: C80DocTypecodes_AdmissionRequestDocument,
  PrescriptionForRehabilitationDocument: C80DocTypecodes_PrescriptionForRehabilitationDocument,
  PrescriptionForDiagnosticOrSpecialistCareDocument: C80DocTypecodes_PrescriptionForDiagnosticOrSpecialistCareDocument,
  PrescriptionForMedicationDocument: C80DocTypecodes_PrescriptionForMedicationDocument,
  PatientTransportationRequestDocument: C80DocTypecodes_PatientTransportationRequestDocument,
  PulmonaryFunctionReport: C80DocTypecodes_PulmonaryFunctionReport,
  AbdomenMRCPWOContrast: C80DocTypecodes_AbdomenMRCPWOContrast,
  HeadToThighPET: C80DocTypecodes_HeadToThighPET,
  HeadAndNeckPET: C80DocTypecodes_HeadAndNeckPET,
  USGuidanceForAblationOfTissueOfUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForAblationOfTissueOfUnspecifiedBodyRegion,
  HeartCT: C80DocTypecodes_HeartCT,
  CoronaryArteriesCTAngiogramAnd3DReconstructionWContrastIV: C80DocTypecodes_CoronaryArteriesCTAngiogramAnd3DReconstructionWContrastIV,
  AVFistulaFluoroscopicAngiogramWContrastIV: C80DocTypecodes_AVFistulaFluoroscopicAngiogramWContrastIV,
  CTGuidanceForAblationOfTissueOfUnspecifiedBodyRegion: C80DocTypecodes_CTGuidanceForAblationOfTissueOfUnspecifiedBodyRegion,
  BrainFunctionalMRI: C80DocTypecodes_BrainFunctionalMRI,
  HeartMRIWStressAndWAndWOContrastIV: C80DocTypecodes_HeartMRIWStressAndWAndWOContrastIV,
  HeartMRIWStress: C80DocTypecodes_HeartMRIWStress,
  LeftAtriumAndPulmonaryVeinsCTAngiogramAnd3DReconstructionWContrastIV: C80DocTypecodes_LeftAtriumAndPulmonaryVeinsCTAngiogramAnd3DReconstructionWContrastIV,
  EmergencyDepartmentDischargeSummary: C80DocTypecodes_EmergencyDepartmentDischargeSummary,
  PsychiatryDischargeSummary: C80DocTypecodes_PsychiatryDischargeSummary,
  NeonatalCareReport: C80DocTypecodes_NeonatalCareReport,
  TransthoracicCardiacEchoStudyReportUS: C80DocTypecodes_TransthoracicCardiacEchoStudyReportUS,
  StressCardiacEchoStudyReportUS: C80DocTypecodes_StressCardiacEchoStudyReportUS,
  WellChildVisitNote: C80DocTypecodes_WellChildVisitNote,
  PatientConsent: C80DocTypecodes_PatientConsent,
  ProcedureIndicationsNarrative: C80DocTypecodes_ProcedureIndicationsNarrative,
  PostprocedureDiagnosisNarrative: C80DocTypecodes_PostprocedureDiagnosisNarrative,
  ProcedureEstimatedBloodLossNarrative: C80DocTypecodes_ProcedureEstimatedBloodLossNarrative,
  ProcedureImplantsNarrative: C80DocTypecodes_ProcedureImplantsNarrative,
  PlannedProcedureNarrative: C80DocTypecodes_PlannedProcedureNarrative,
  ProcedureSpecimensTakenNarrative: C80DocTypecodes_ProcedureSpecimensTakenNarrative,
  ProcedureAnesthesiaNarrative: C80DocTypecodes_ProcedureAnesthesiaNarrative,
  ProcedureDispositionNarrative: C80DocTypecodes_ProcedureDispositionNarrative,
  ProcedureFindingsNarrative: C80DocTypecodes_ProcedureFindingsNarrative,
  FDAPackageInsertInstructionsForUseSection: C80DocTypecodes_FDAPackageInsertInstructionsForUseSection,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma8thSpecimenPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma8thSpecimenPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma7thSpecimenPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma7thSpecimenPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma6thSpecimenPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma6thSpecimenPostXXXChallenge,
  VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma5thSpecimenPostXXXChallenge: C80DocTypecodes_VAL11DeoxycorticosteroneMassVolumeInSerumOrPlasma5thSpecimenPostXXXChallenge,
  EmergencyDepartmentDischargeInstructions: C80DocTypecodes_EmergencyDepartmentDischargeInstructions,
  RectumAndColonCT3DWAirContrastPR: C80DocTypecodes_RectumAndColonCT3DWAirContrastPR,
  ThyroidScanAndUptakeWI123PO: C80DocTypecodes_ThyroidScanAndUptakeWI123PO,
  FDAPackageInsertAccessories: C80DocTypecodes_FDAPackageInsertAccessories,
  FDAPackageInsertAssemblyOrInstallationInstructions: C80DocTypecodes_FDAPackageInsertAssemblyOrInstallationInstructions,
  FDAPackageInsertCalibrationInstructions: C80DocTypecodes_FDAPackageInsertCalibrationInstructions,
  FDAPackageInsertCleaningDisinfectingAndSterilizationInstructions: C80DocTypecodes_FDAPackageInsertCleaningDisinfectingAndSterilizationInstructions,
  FDAPackageInsertComponents: C80DocTypecodes_FDAPackageInsertComponents,
  FDAPackageInsertIntendedUseOfTheDevice: C80DocTypecodes_FDAPackageInsertIntendedUseOfTheDevice,
  FDAPackageInsertOtherSafetyInformation: C80DocTypecodes_FDAPackageInsertOtherSafetyInformation,
  SynopticReport: C80DocTypecodes_SynopticReport,
  ReportAddendumSynopticDocument: C80DocTypecodes_ReportAddendumSynopticDocument,
  PathologyConsultNote: C80DocTypecodes_PathologyConsultNote,
  PathologyConsultNoteSynoptic: C80DocTypecodes_PathologyConsultNoteSynoptic,
  ReportTemplateID: C80DocTypecodes_ReportTemplateID,
  ReportTemplateSource: C80DocTypecodes_ReportTemplateSource,
  ReportTemplateVersionID: C80DocTypecodes_ReportTemplateVersionID,
  MedicationDispensedBriefDocument: C80DocTypecodes_MedicationDispensedBriefDocument,
  PatientSummaryDocument: C80DocTypecodes_PatientSummaryDocument,
  PatientSummaryUnexpectedContactDocument: C80DocTypecodes_PatientSummaryUnexpectedContactDocument,
  MedicationDispensedExtendedDocument: C80DocTypecodes_MedicationDispensedExtendedDocument,
  FDAProductLabelPlasmaDerivative: C80DocTypecodes_FDAProductLabelPlasmaDerivative,
  FDAProductLabelCellularTherapy: C80DocTypecodes_FDAProductLabelCellularTherapy,
  FDAPackageInsertIndexingPharmacologicClass: C80DocTypecodes_FDAPackageInsertIndexingPharmacologicClass,
  NurseSummaryNote: C80DocTypecodes_NurseSummaryNote,
  GoalsNarrative: C80DocTypecodes_GoalsNarrative,
  ExpectedOutcomesNarrative: C80DocTypecodes_ExpectedOutcomesNarrative,
  ObjectiveNarrative: C80DocTypecodes_ObjectiveNarrative,
  SubjectiveNarrative: C80DocTypecodes_SubjectiveNarrative,
  MedicationPharmaceuticalAdviceExtendedDocument: C80DocTypecodes_MedicationPharmaceuticalAdviceExtendedDocument,
  MedicationPharmaceuticalAdviceBriefDocument: C80DocTypecodes_MedicationPharmaceuticalAdviceBriefDocument,
  PatientSurgicalOperationConsent: C80DocTypecodes_PatientSurgicalOperationConsent,
  PatientAnesthesiaConsent: C80DocTypecodes_PatientAnesthesiaConsent,
  RecommendationInterpretationDocument: C80DocTypecodes_RecommendationInterpretationDocument,
  InterventionsNarrative: C80DocTypecodes_InterventionsNarrative,
  RenalArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_RenalArteryLeftFluoroscopicAngiogramWContrastIA,
  RenalArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_RenalArteryRightFluoroscopicAngiogramWContrastIA,
  HeadArteryLeftPlusNeckArteryLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_HeadArteryLeftPlusNeckArteryLeftFluoroscopicAngiogramWContrastIA,
  HeadArteryRightPlusNeckArteryRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_HeadArteryRightPlusNeckArteryRightFluoroscopicAngiogramWContrastIA,
  FluoroscopicAngiogramGuidanceForPlacementOfIntraperitonealCatheterInAbdomen: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfIntraperitonealCatheterInAbdomen,
  ExtremityLeftUSLimited: C80DocTypecodes_ExtremityLeftUSLimited,
  ExtremityRightUSLimited: C80DocTypecodes_ExtremityRightUSLimited,
  FluoroscopicAngiogramGuidanceForPlacementOfIlioIliacTubeEndoprosthesisInIliacArteryLeftWContrastIA: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfIlioIliacTubeEndoprosthesisInIliacArteryLeftWContrastIA,
  FluoroscopicAngiogramGuidanceForPlacementOfIlioIliacTubeEndoprosthesisInIliacArteryRightWContrastIA: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfIlioIliacTubeEndoprosthesisInIliacArteryRightWContrastIA,
  USGuidanceForPercutaneousDrainageOfCavity: C80DocTypecodes_USGuidanceForPercutaneousDrainageOfCavity,
  ComputerGeneratedRecommendationDocument: C80DocTypecodes_ComputerGeneratedRecommendationDocument,
  BreastLymphaticsLeftScanWRadionuclideIntraLymphatic: C80DocTypecodes_BreastLymphaticsLeftScanWRadionuclideIntraLymphatic,
  BreastLymphaticsRightScanWRadionuclideIntraLymphatic: C80DocTypecodes_BreastLymphaticsRightScanWRadionuclideIntraLymphatic,
  GeneralMedicineHospitalAdmissionEvaluationNote: C80DocTypecodes_GeneralMedicineHospitalAdmissionEvaluationNote,
  GeneralMedicineMedicalStudentHospitalAdmissionEvaluationNote: C80DocTypecodes_GeneralMedicineMedicalStudentHospitalAdmissionEvaluationNote,
  GeneralMedicineMedicalStudentHospitalProgressNote: C80DocTypecodes_GeneralMedicineMedicalStudentHospitalProgressNote,
  GeneralMedicineMedicalStudentHospitalConsultNote: C80DocTypecodes_GeneralMedicineMedicalStudentHospitalConsultNote,
  SurgeryHospitalProgressNote: C80DocTypecodes_SurgeryHospitalProgressNote,
  CriticalCareMedicineHospitalAdmissionEvaluationNote: C80DocTypecodes_CriticalCareMedicineHospitalAdmissionEvaluationNote,
  CriticalCareMedicineHospitalProgressNote: C80DocTypecodes_CriticalCareMedicineHospitalProgressNote,
  ThoracicSurgeryHospitalAdmissionEvaluationNote: C80DocTypecodes_ThoracicSurgeryHospitalAdmissionEvaluationNote,
  ThoracicSurgeryHospitalProgressNote: C80DocTypecodes_ThoracicSurgeryHospitalProgressNote,
  PulmonaryHospitalAdmissionEvaluationNote: C80DocTypecodes_PulmonaryHospitalAdmissionEvaluationNote,
  PulmonaryHospitalProgressNote: C80DocTypecodes_PulmonaryHospitalProgressNote,
  PastoralCareHospitalAssessmentNote: C80DocTypecodes_PastoralCareHospitalAssessmentNote,
  CaseManagerHospitalInitialAssessmentNote: C80DocTypecodes_CaseManagerHospitalInitialAssessmentNote,
  SurgeryMedicalStudentHospitalAdmissionEvaluationNote: C80DocTypecodes_SurgeryMedicalStudentHospitalAdmissionEvaluationNote,
  SurgeryMedicalStudentHospitalProgressNote: C80DocTypecodes_SurgeryMedicalStudentHospitalProgressNote,
  SurgeryMedicalStudentHospitalConsultNote: C80DocTypecodes_SurgeryMedicalStudentHospitalConsultNote,
  CriticalCareMedicinePhysicianAttendingHospitalNote: C80DocTypecodes_CriticalCareMedicinePhysicianAttendingHospitalNote,
  CriticalCareMedicineMedicalStudentHospitalAdmissionEvaluationNote: C80DocTypecodes_CriticalCareMedicineMedicalStudentHospitalAdmissionEvaluationNote,
  CriticalCareMedicineMedicalStudentHospitalProgressNote: C80DocTypecodes_CriticalCareMedicineMedicalStudentHospitalProgressNote,
  CriticalCareMedicineMedicalStudentHospitalConsultNote: C80DocTypecodes_CriticalCareMedicineMedicalStudentHospitalConsultNote,
  ThoracicSurgeryPhysicianAttendingHospitalNote: C80DocTypecodes_ThoracicSurgeryPhysicianAttendingHospitalNote,
  ThoracicSurgeryMedicalStudentHospitalAdmissionEvaluationNote: C80DocTypecodes_ThoracicSurgeryMedicalStudentHospitalAdmissionEvaluationNote,
  ThoracicSurgeryMedicalStudentHospitalProgressNote: C80DocTypecodes_ThoracicSurgeryMedicalStudentHospitalProgressNote,
  ThoracicSurgeryMedicalStudentHospitalConsultNote: C80DocTypecodes_ThoracicSurgeryMedicalStudentHospitalConsultNote,
  PulmonaryPhysicianAttendingHospitalNote: C80DocTypecodes_PulmonaryPhysicianAttendingHospitalNote,
  PulmonaryMedicalStudentHospitalAdmissionEvaluationNote: C80DocTypecodes_PulmonaryMedicalStudentHospitalAdmissionEvaluationNote,
  PulmonaryMedicalStudentHospitalProgressNote: C80DocTypecodes_PulmonaryMedicalStudentHospitalProgressNote,
  PulmonaryMedicalStudentHospitalConsultNote: C80DocTypecodes_PulmonaryMedicalStudentHospitalConsultNote,
  FDAPackageInsertIndexingAdverseReaction: C80DocTypecodes_FDAPackageInsertIndexingAdverseReaction,
  FDAPackageInsertIndexingSubstance: C80DocTypecodes_FDAPackageInsertIndexingSubstance,
  RenalVesselsLeftFluoroscopicAngiogramWContrast: C80DocTypecodes_RenalVesselsLeftFluoroscopicAngiogramWContrast,
  RenalVesselsRightFluoroscopicAngiogramWContrast: C80DocTypecodes_RenalVesselsRightFluoroscopicAngiogramWContrast,
  HospitalEvaluationAndManagementOfSmokingCessation: C80DocTypecodes_HospitalEvaluationAndManagementOfSmokingCessation,
  ReadinessForDutyAssessment: C80DocTypecodes_ReadinessForDutyAssessment,
  MedicalHistoryScreeningForm: C80DocTypecodes_MedicalHistoryScreeningForm,
  PrescriptionForEyewear: C80DocTypecodes_PrescriptionForEyewear,
  HealthRecordCoverSheet: C80DocTypecodes_HealthRecordCoverSheet,
  HealthInsuranceCard: C80DocTypecodes_HealthInsuranceCard,
  HealthInsuranceRelatedForm: C80DocTypecodes_HealthInsuranceRelatedForm,
  ReleaseOfInformationConsent: C80DocTypecodes_ReleaseOfInformationConsent,
  ProcedureConsent: C80DocTypecodes_ProcedureConsent,
  ReadinessForDutyLetter: C80DocTypecodes_ReadinessForDutyLetter,
  NursePlanOfCareNote: C80DocTypecodes_NursePlanOfCareNote,
  PersonalHealthMonitoringReportAutomated: C80DocTypecodes_PersonalHealthMonitoringReportAutomated,
  DeathCertificate: C80DocTypecodes_DeathCertificate,
  PowerOfAttorney: C80DocTypecodes_PowerOfAttorney,
  LegalDocument: C80DocTypecodes_LegalDocument,
  OrganDonationConsent: C80DocTypecodes_OrganDonationConsent,
  USGuidanceForPlacementOfNeedleInUnspecifiedBodyRegion: C80DocTypecodes_USGuidanceForPlacementOfNeedleInUnspecifiedBodyRegion,
  MammaryArteryInternalLeftFluoroscopicAngiogramWContrastIA: C80DocTypecodes_MammaryArteryInternalLeftFluoroscopicAngiogramWContrastIA,
  LungLeftXRayWContrastIntrabronchial: C80DocTypecodes_LungLeftXRayWContrastIntrabronchial,
  LungRightXRayWContrastIntrabronchial: C80DocTypecodes_LungRightXRayWContrastIntrabronchial,
  FluoroscopyGuidanceForCatheterizationOfFallopianTubeLeftTranscervical: C80DocTypecodes_FluoroscopyGuidanceForCatheterizationOfFallopianTubeLeftTranscervical,
  FluoroscopyGuidanceForCatheterizationOfFallopianTubeRightTranscervical: C80DocTypecodes_FluoroscopyGuidanceForCatheterizationOfFallopianTubeRightTranscervical,
  MammaryArteryInternalRightFluoroscopicAngiogramWContrastIA: C80DocTypecodes_MammaryArteryInternalRightFluoroscopicAngiogramWContrastIA,
  FluoroscopicAngiogramGuidanceForPlacementOfStentInArteryLeft: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInArteryLeft,
  FluoroscopicAngiogramGuidanceForPlacementOfStentInArteryRight: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInArteryRight,
  KidneyBilateralFluoroscopyViewForCystExamination: C80DocTypecodes_KidneyBilateralFluoroscopyViewForCystExamination,
  KidneyLeftFluoroscopyViewForCystExamination: C80DocTypecodes_KidneyLeftFluoroscopyViewForCystExamination,
  KidneyRightFluoroscopyViewForCystExamination: C80DocTypecodes_KidneyRightFluoroscopyViewForCystExamination,
  SagittalSinusAndJugularVeinsLeftFluoroscopicAngiogramWContrastIV: C80DocTypecodes_SagittalSinusAndJugularVeinsLeftFluoroscopicAngiogramWContrastIV,
  SagittalSinusVeinLeftFluoroscopicAngiogramWContrastIV: C80DocTypecodes_SagittalSinusVeinLeftFluoroscopicAngiogramWContrastIV,
  SagittalSinusVeinRightFluoroscopicAngiogramWContrastIV: C80DocTypecodes_SagittalSinusVeinRightFluoroscopicAngiogramWContrastIV,
  SagittalSinusAndJugularVeinsRightFluoroscopicAngiogramWContrastIV: C80DocTypecodes_SagittalSinusAndJugularVeinsRightFluoroscopicAngiogramWContrastIV,
  InhalationChallengeTestReportDocumentWMethacholineInhaled: C80DocTypecodes_InhalationChallengeTestReportDocumentWMethacholineInhaled,
  FDAPackageInsertLotDistributionData: C80DocTypecodes_FDAPackageInsertLotDistributionData,
  FDAPackageInsertPharmacogenomicsSection: C80DocTypecodes_FDAPackageInsertPharmacogenomicsSection,
  EMSPatientCareReportVersion31DocumentNEMSIS: C80DocTypecodes_EMSPatientCareReportVersion31DocumentNEMSIS,
  AdmissionEvaluationNote: C80DocTypecodes_AdmissionEvaluationNote,
  HospitalAdmissionEvaluationNote: C80DocTypecodes_HospitalAdmissionEvaluationNote,
  GeriatricMedicineEducationNote: C80DocTypecodes_GeriatricMedicineEducationNote,
  OutpatientEducationNote: C80DocTypecodes_OutpatientEducationNote,
  NursingFacilityHistoryAndPhysicalNote: C80DocTypecodes_NursingFacilityHistoryAndPhysicalNote,
  PostoperativeEvaluationAndManagementNote: C80DocTypecodes_PostoperativeEvaluationAndManagementNote,
  OphthalmologyPostoperativeEvaluationAndManagementNote: C80DocTypecodes_OphthalmologyPostoperativeEvaluationAndManagementNote,
  PreoperativeEvaluationAndManagementNote: C80DocTypecodes_PreoperativeEvaluationAndManagementNote,
  OutpatientSupervisoryNote: C80DocTypecodes_OutpatientSupervisoryNote,
  PastoralCareHospitalConsultNote: C80DocTypecodes_PastoralCareHospitalConsultNote,
  RespiratoryTherapyHospitalInitialAssessmentNote: C80DocTypecodes_RespiratoryTherapyHospitalInitialAssessmentNote,
  CardiologyHospitalAdmissionEvaluationNote: C80DocTypecodes_CardiologyHospitalAdmissionEvaluationNote,
  CardiologyHospitalProgressNote: C80DocTypecodes_CardiologyHospitalProgressNote,
  CriticalCareMedicinePhysicianAttendingHospitalProgressNote: C80DocTypecodes_CriticalCareMedicinePhysicianAttendingHospitalProgressNote,
  PhysicianHospitalRestraintNote: C80DocTypecodes_PhysicianHospitalRestraintNote,
  GeneralMedicinePhysicianAttendingHospitalProgressNote: C80DocTypecodes_GeneralMedicinePhysicianAttendingHospitalProgressNote,
  NurseHospitalRestraintNote: C80DocTypecodes_NurseHospitalRestraintNote,
  NurseHospitalEducationNote: C80DocTypecodes_NurseHospitalEducationNote,
  PulmonaryPhysicianAttendingHospitalProgressNote: C80DocTypecodes_PulmonaryPhysicianAttendingHospitalProgressNote,
  RespiratoryTherapyHospitalProgressNote: C80DocTypecodes_RespiratoryTherapyHospitalProgressNote,
  SurgeryPhysicianAttendingHospitalProgressNote: C80DocTypecodes_SurgeryPhysicianAttendingHospitalProgressNote,
  ThoracicSurgeryPhysicianAttendingHospitalProgressNote: C80DocTypecodes_ThoracicSurgeryPhysicianAttendingHospitalProgressNote,
  NurseHospitalTransferSummaryNote: C80DocTypecodes_NurseHospitalTransferSummaryNote,
  CardiologyMedicalStudentHospitalAdmissionEvaluationNote: C80DocTypecodes_CardiologyMedicalStudentHospitalAdmissionEvaluationNote,
  CardiologyPhysicianAttendingHospitalProgressNote: C80DocTypecodes_CardiologyPhysicianAttendingHospitalProgressNote,
  CardiologyMedicalStudentHospitalProgressNote: C80DocTypecodes_CardiologyMedicalStudentHospitalProgressNote,
  CardiologyMedicalStudentHospitalConsultNote: C80DocTypecodes_CardiologyMedicalStudentHospitalConsultNote,
  DermatologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_DermatologyPreoperativeEvaluationAndManagementNote,
  DermatologyHospitalConsultNote: C80DocTypecodes_DermatologyHospitalConsultNote,
  EmergencyMedicineEmergencyDepartmentAdmissionEvaluationNote: C80DocTypecodes_EmergencyMedicineEmergencyDepartmentAdmissionEvaluationNote,
  HematologyPlusMedicalOncologyInitialAssessmentNote: C80DocTypecodes_HematologyPlusMedicalOncologyInitialAssessmentNote,
  HematologyPlusMedicalOncologyProgressNote: C80DocTypecodes_HematologyPlusMedicalOncologyProgressNote,
  HematologyPlusMedicalOncologyHospitalLetter: C80DocTypecodes_HematologyPlusMedicalOncologyHospitalLetter,
  NeurologyDiagnosticStudyNote: C80DocTypecodes_NeurologyDiagnosticStudyNote,
  ObstetricsAndGynecologyDiagnosticStudyNote: C80DocTypecodes_ObstetricsAndGynecologyDiagnosticStudyNote,
  ObstetricsAndGynecologyDischargeSummary: C80DocTypecodes_ObstetricsAndGynecologyDischargeSummary,
  ObstetricsAndGynecologyHistoryAndPhysicalNote: C80DocTypecodes_ObstetricsAndGynecologyHistoryAndPhysicalNote,
  ObstetricsAndGynecologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_ObstetricsAndGynecologyPreoperativeEvaluationAndManagementNote,
  ObstetricsAndGynecologyProcedureNote: C80DocTypecodes_ObstetricsAndGynecologyProcedureNote,
  ObstetricsAndGynecologyProgressNote: C80DocTypecodes_ObstetricsAndGynecologyProgressNote,
  ObstetricsAndGynecologyTransferSummaryNote: C80DocTypecodes_ObstetricsAndGynecologyTransferSummaryNote,
  ObstetricsAndGynecologyHospitalConsultNote: C80DocTypecodes_ObstetricsAndGynecologyHospitalConsultNote,
  ObstetricsAndGynecologyHospitalLetter: C80DocTypecodes_ObstetricsAndGynecologyHospitalLetter,
  OccupationalTherapyTransferSummaryNote: C80DocTypecodes_OccupationalTherapyTransferSummaryNote,
  OccupationalTherapyHospitalConsultNote: C80DocTypecodes_OccupationalTherapyHospitalConsultNote,
  OccupationalTherapyHospitalLetter: C80DocTypecodes_OccupationalTherapyHospitalLetter,
  OphthalmologyDischargeSummary: C80DocTypecodes_OphthalmologyDischargeSummary,
  OphthalmologyHistoryAndPhysicalNote: C80DocTypecodes_OphthalmologyHistoryAndPhysicalNote,
  OphthalmologyProgressNote: C80DocTypecodes_OphthalmologyProgressNote,
  OphthalmologyHospitalConsultNote: C80DocTypecodes_OphthalmologyHospitalConsultNote,
  OphthalmologyHospitalLetter: C80DocTypecodes_OphthalmologyHospitalLetter,
  OrthopaedicSurgeryDiagnosticStudyNote: C80DocTypecodes_OrthopaedicSurgeryDiagnosticStudyNote,
  OrthopaedicSurgeryDischargeSummary: C80DocTypecodes_OrthopaedicSurgeryDischargeSummary,
  OrthopaedicSurgeryHistoryAndPhysicalNote: C80DocTypecodes_OrthopaedicSurgeryHistoryAndPhysicalNote,
  OrthopaedicSurgeryPreoperativeEvaluationAndManagementNote: C80DocTypecodes_OrthopaedicSurgeryPreoperativeEvaluationAndManagementNote,
  OrthopaedicSurgeryProgressNote: C80DocTypecodes_OrthopaedicSurgeryProgressNote,
  OrthopaedicSurgeryTransferSummaryNote: C80DocTypecodes_OrthopaedicSurgeryTransferSummaryNote,
  OrthopaedicSurgeryHospitalLetter: C80DocTypecodes_OrthopaedicSurgeryHospitalLetter,
  PharmacyHospitalConsultNote: C80DocTypecodes_PharmacyHospitalConsultNote,
  PharmacyHospitalMedicationManagementNote: C80DocTypecodes_PharmacyHospitalMedicationManagementNote,
  PhysicalTherapyHospitalConsultNote: C80DocTypecodes_PhysicalTherapyHospitalConsultNote,
  PlasticSurgeryDischargeSummary: C80DocTypecodes_PlasticSurgeryDischargeSummary,
  PlasticSurgeryHistoryAndPhysicalNote: C80DocTypecodes_PlasticSurgeryHistoryAndPhysicalNote,
  PlasticSurgeryLetter: C80DocTypecodes_PlasticSurgeryLetter,
  PlasticSurgeryPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PlasticSurgeryPreoperativeEvaluationAndManagementNote,
  PlasticSurgeryProgressNote: C80DocTypecodes_PlasticSurgeryProgressNote,
  PlasticSurgeryTransferSummaryNote: C80DocTypecodes_PlasticSurgeryTransferSummaryNote,
  PlasticSurgeryHospitalConsultNote: C80DocTypecodes_PlasticSurgeryHospitalConsultNote,
  PlasticSurgeryHospitalLetter: C80DocTypecodes_PlasticSurgeryHospitalLetter,
  PsychiatryHistoryAndPhysicalNote: C80DocTypecodes_PsychiatryHistoryAndPhysicalNote,
  PsychiatryOutpatientNote: C80DocTypecodes_PsychiatryOutpatientNote,
  RadiationOncologySummaryNote: C80DocTypecodes_RadiationOncologySummaryNote,
  RadiationOncologyHospitalSummaryNote: C80DocTypecodes_RadiationOncologyHospitalSummaryNote,
  RadiologyDiagnosticStudyNote: C80DocTypecodes_RadiologyDiagnosticStudyNote,
  RecreationalTherapyHospitalEducationNote: C80DocTypecodes_RecreationalTherapyHospitalEducationNote,
  SurgeryHospitalPostoperativeEvaluationAndManagementNote: C80DocTypecodes_SurgeryHospitalPostoperativeEvaluationAndManagementNote,
  ProgressLetter: C80DocTypecodes_ProgressLetter,
  SummaryNote: C80DocTypecodes_SummaryNote,
  HospitalLetter: C80DocTypecodes_HospitalLetter,
  HospitalPostoperativeEvaluationAndManagementNote: C80DocTypecodes_HospitalPostoperativeEvaluationAndManagementNote,
  AdolescentMedicineDiagnosticStudyNote: C80DocTypecodes_AdolescentMedicineDiagnosticStudyNote,
  AdolescentMedicineDischargeSummary: C80DocTypecodes_AdolescentMedicineDischargeSummary,
  AdolescentMedicineHistoryAndPhysicalNote: C80DocTypecodes_AdolescentMedicineHistoryAndPhysicalNote,
  AdolescentMedicineNote: C80DocTypecodes_AdolescentMedicineNote,
  AdolescentMedicinePreoperativeEvaluationAndManagementNote: C80DocTypecodes_AdolescentMedicinePreoperativeEvaluationAndManagementNote,
  AdolescentMedicineProgressNote: C80DocTypecodes_AdolescentMedicineProgressNote,
  AdolescentMedicineTransferSummaryNote: C80DocTypecodes_AdolescentMedicineTransferSummaryNote,
  AdolescentMedicineHospitalConsultNote: C80DocTypecodes_AdolescentMedicineHospitalConsultNote,
  AdolescentMedicineHospitalLetter: C80DocTypecodes_AdolescentMedicineHospitalLetter,
  AdvancedHeartFailureAndTransplantCardiologyNote: C80DocTypecodes_AdvancedHeartFailureAndTransplantCardiologyNote,
  AdvancedHeartFailureAndTransplantCardiologyHistoryAndPhysicalNote: C80DocTypecodes_AdvancedHeartFailureAndTransplantCardiologyHistoryAndPhysicalNote,
  AdvancedHeartFailureAndTransplantCardiologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_AdvancedHeartFailureAndTransplantCardiologyPreoperativeEvaluationAndManagementNote,
  AdvancedHeartFailureAndTransplantCardiologyHospitalLetter: C80DocTypecodes_AdvancedHeartFailureAndTransplantCardiologyHospitalLetter,
  AllergyAndImmunologyDiagnosticStudyNote: C80DocTypecodes_AllergyAndImmunologyDiagnosticStudyNote,
  AllergyAndImmunologyDischargeSummary: C80DocTypecodes_AllergyAndImmunologyDischargeSummary,
  AllergyAndImmunologyHistoryAndPhysicalNote: C80DocTypecodes_AllergyAndImmunologyHistoryAndPhysicalNote,
  AllergyAndImmunologyNote: C80DocTypecodes_AllergyAndImmunologyNote,
  AllergyAndImmunologyProcedureNote: C80DocTypecodes_AllergyAndImmunologyProcedureNote,
  AllergyAndImmunologyProgressNote: C80DocTypecodes_AllergyAndImmunologyProgressNote,
  AllergyAndImmunologyTransferSummaryNote: C80DocTypecodes_AllergyAndImmunologyTransferSummaryNote,
  AllergyAndImmunologyHospitalConsultNote: C80DocTypecodes_AllergyAndImmunologyHospitalConsultNote,
  AllergyAndImmunologyHospitalLetter: C80DocTypecodes_AllergyAndImmunologyHospitalLetter,
  AudiologyDiagnosticStudyNote: C80DocTypecodes_AudiologyDiagnosticStudyNote,
  AudiologyNote: C80DocTypecodes_AudiologyNote,
  AudiologyHistoryAndPhysicalNote: C80DocTypecodes_AudiologyHistoryAndPhysicalNote,
  AudiologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_AudiologyPreoperativeEvaluationAndManagementNote,
  AudiologyHospitalConsultNote: C80DocTypecodes_AudiologyHospitalConsultNote,
  AudiologyHospitalDiagnosticStudyNote: C80DocTypecodes_AudiologyHospitalDiagnosticStudyNote,
  ChildAndAdolescentPsychiatryDiagnosticStudyNote: C80DocTypecodes_ChildAndAdolescentPsychiatryDiagnosticStudyNote,
  ChildAndAdolescentPsychiatryDischargeSummary: C80DocTypecodes_ChildAndAdolescentPsychiatryDischargeSummary,
  ChildAndAdolescentPsychiatryHistoryAndPhysicalNote: C80DocTypecodes_ChildAndAdolescentPsychiatryHistoryAndPhysicalNote,
  ChildAndAdolescentPsychiatryNote: C80DocTypecodes_ChildAndAdolescentPsychiatryNote,
  ChildAndAdolescentPsychiatryProgressNote: C80DocTypecodes_ChildAndAdolescentPsychiatryProgressNote,
  ChildAndAdolescentPsychiatryTransferSummaryNote: C80DocTypecodes_ChildAndAdolescentPsychiatryTransferSummaryNote,
  ChildAndAdolescentPsychiatryHospitalConsultNote: C80DocTypecodes_ChildAndAdolescentPsychiatryHospitalConsultNote,
  ChildAndAdolescentPsychiatryHospitalLetter: C80DocTypecodes_ChildAndAdolescentPsychiatryHospitalLetter,
  ClinicalBiochemicalGeneticsNote: C80DocTypecodes_ClinicalBiochemicalGeneticsNote,
  ClinicalBiochemicalGeneticsHospitalConsultNote: C80DocTypecodes_ClinicalBiochemicalGeneticsHospitalConsultNote,
  ClinicalGeneticsDiagnosticStudyNote: C80DocTypecodes_ClinicalGeneticsDiagnosticStudyNote,
  ClinicalGeneticsDischargeSummary: C80DocTypecodes_ClinicalGeneticsDischargeSummary,
  ClinicalGeneticsHistoryAndPhysicalNote: C80DocTypecodes_ClinicalGeneticsHistoryAndPhysicalNote,
  ClinicalGeneticsNote: C80DocTypecodes_ClinicalGeneticsNote,
  ClinicalGeneticsPreoperativeEvaluationAndManagementNote: C80DocTypecodes_ClinicalGeneticsPreoperativeEvaluationAndManagementNote,
  ClinicalGeneticsProcedureNote: C80DocTypecodes_ClinicalGeneticsProcedureNote,
  ClinicalGeneticsProgressNote: C80DocTypecodes_ClinicalGeneticsProgressNote,
  ClinicalGeneticsTransferSummaryNote: C80DocTypecodes_ClinicalGeneticsTransferSummaryNote,
  ClinicalGeneticsHospitalConsultNote: C80DocTypecodes_ClinicalGeneticsHospitalConsultNote,
  ClinicalGeneticsHospitalLetter: C80DocTypecodes_ClinicalGeneticsHospitalLetter,
  DevelopmentalBehavioralPediatricsDischargeSummary: C80DocTypecodes_DevelopmentalBehavioralPediatricsDischargeSummary,
  DevelopmentalBehavioralPediatricsHistoryAndPhysicalNote: C80DocTypecodes_DevelopmentalBehavioralPediatricsHistoryAndPhysicalNote,
  DevelopmentalBehavioralPediatricsNote: C80DocTypecodes_DevelopmentalBehavioralPediatricsNote,
  DevelopmentalBehavioralPediatricsProcedureNote: C80DocTypecodes_DevelopmentalBehavioralPediatricsProcedureNote,
  DevelopmentalBehavioralPediatricsProgressNote: C80DocTypecodes_DevelopmentalBehavioralPediatricsProgressNote,
  DevelopmentalBehavioralPediatricsTransferSummaryNote: C80DocTypecodes_DevelopmentalBehavioralPediatricsTransferSummaryNote,
  DevelopmentalBehavioralPediatricsHospitalConsultNote: C80DocTypecodes_DevelopmentalBehavioralPediatricsHospitalConsultNote,
  DevelopmentalBehavioralPediatricsHospitalLetter: C80DocTypecodes_DevelopmentalBehavioralPediatricsHospitalLetter,
  GeriatricMedicineSkilledNursingFacilityNote: C80DocTypecodes_GeriatricMedicineSkilledNursingFacilityNote,
  MultiSpecialtyProgramDiagnosticStudyNote: C80DocTypecodes_MultiSpecialtyProgramDiagnosticStudyNote,
  MultiSpecialtyProgramDischargeSummary: C80DocTypecodes_MultiSpecialtyProgramDischargeSummary,
  MultiSpecialtyProgramHistoryAndPhysicalNote: C80DocTypecodes_MultiSpecialtyProgramHistoryAndPhysicalNote,
  MultiSpecialtyProgramNote: C80DocTypecodes_MultiSpecialtyProgramNote,
  MultiSpecialtyProgramPreoperativeEvaluationAndManagementNote: C80DocTypecodes_MultiSpecialtyProgramPreoperativeEvaluationAndManagementNote,
  MultiSpecialtyProgramProgressNote: C80DocTypecodes_MultiSpecialtyProgramProgressNote,
  MultiSpecialtyProgramTransferSummaryNote: C80DocTypecodes_MultiSpecialtyProgramTransferSummaryNote,
  MultiSpecialtyProgramHospitalConsultNote: C80DocTypecodes_MultiSpecialtyProgramHospitalConsultNote,
  MultiSpecialtyProgramHospitalLetter: C80DocTypecodes_MultiSpecialtyProgramHospitalLetter,
  NeonatalPerinatalMedicineHistoryAndPhysicalNote: C80DocTypecodes_NeonatalPerinatalMedicineHistoryAndPhysicalNote,
  NeonatalPerinatalMedicineLetter: C80DocTypecodes_NeonatalPerinatalMedicineLetter,
  NeonatalPerinatalMedicineHospitalConsultNote: C80DocTypecodes_NeonatalPerinatalMedicineHospitalConsultNote,
  NeonatalPerinatalMedicineHospitalLetter: C80DocTypecodes_NeonatalPerinatalMedicineHospitalLetter,
  NeurologicalSurgeryDiagnosticStudyNote: C80DocTypecodes_NeurologicalSurgeryDiagnosticStudyNote,
  NeurologicalSurgeryDischargeSummary: C80DocTypecodes_NeurologicalSurgeryDischargeSummary,
  NeurologicalSurgeryHistoryAndPhysicalNote: C80DocTypecodes_NeurologicalSurgeryHistoryAndPhysicalNote,
  NeurologicalSurgeryPreoperativeEvaluationAndManagementNote: C80DocTypecodes_NeurologicalSurgeryPreoperativeEvaluationAndManagementNote,
  NeurologicalSurgeryProcedureNote: C80DocTypecodes_NeurologicalSurgeryProcedureNote,
  NeurologicalSurgeryProgressNote: C80DocTypecodes_NeurologicalSurgeryProgressNote,
  NeurologicalSurgeryHospitalConsultNote: C80DocTypecodes_NeurologicalSurgeryHospitalConsultNote,
  NeurologicalSurgeryHospitalLetter: C80DocTypecodes_NeurologicalSurgeryHospitalLetter,
  NeurologyWithSpecialQualificationsInChildNeurologyDiagnosticStudyNote: C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyDiagnosticStudyNote,
  NeurologyWithSpecialQualificationsInChildNeurologyDischargeSummary: C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyDischargeSummary,
  NeurologyWithSpecialQualificationsInChildNeurologyHistoryAndPhysicalNote: C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyHistoryAndPhysicalNote,
  NeurologyWithSpecialQualificationsInChildNeurologyNote: C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyNote,
  NeurologyWithSpecialQualificationsInChildNeurologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyPreoperativeEvaluationAndManagementNote,
  NeurologyWithSpecialQualificationsInChildNeurologyProcedureNote: C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyProcedureNote,
  NeurologyWithSpecialQualificationsInChildNeurologyProgressNote: C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyProgressNote,
  NeurologyWithSpecialQualificationsInChildNeurologyTransferSummaryNote: C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyTransferSummaryNote,
  NeurologyWithSpecialQualificationsInChildNeurologyHospitalConsultNote: C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyHospitalConsultNote,
  NeurologyWithSpecialQualificationsInChildNeurologyHospitalDiagnosticStudyNote: C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyHospitalDiagnosticStudyNote,
  NeurologyWithSpecialQualificationsInChildNeurologyHospitalLetter: C80DocTypecodes_NeurologyWithSpecialQualificationsInChildNeurologyHospitalLetter,
  PainMedicineDiagnosticStudyNote: C80DocTypecodes_PainMedicineDiagnosticStudyNote,
  PainMedicineDischargeSummary: C80DocTypecodes_PainMedicineDischargeSummary,
  PainMedicineHistoryAndPhysicalNote: C80DocTypecodes_PainMedicineHistoryAndPhysicalNote,
  PainMedicinePreoperativeEvaluationAndManagementNote: C80DocTypecodes_PainMedicinePreoperativeEvaluationAndManagementNote,
  PainMedicineProcedureNote: C80DocTypecodes_PainMedicineProcedureNote,
  PainMedicineTransferSummaryNote: C80DocTypecodes_PainMedicineTransferSummaryNote,
  PainMedicineHospitalConsultNote: C80DocTypecodes_PainMedicineHospitalConsultNote,
  PainMedicineHospitalLetter: C80DocTypecodes_PainMedicineHospitalLetter,
  PediatricCardiologyDiagnosticStudyNote: C80DocTypecodes_PediatricCardiologyDiagnosticStudyNote,
  PediatricCardiologyDischargeSummary: C80DocTypecodes_PediatricCardiologyDischargeSummary,
  PediatricCardiologyHistoryAndPhysicalNote: C80DocTypecodes_PediatricCardiologyHistoryAndPhysicalNote,
  PediatricCardiologyNote: C80DocTypecodes_PediatricCardiologyNote,
  PediatricCardiologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricCardiologyPreoperativeEvaluationAndManagementNote,
  PediatricCardiologyProcedureNote: C80DocTypecodes_PediatricCardiologyProcedureNote,
  PediatricCardiologyProgressNote: C80DocTypecodes_PediatricCardiologyProgressNote,
  PediatricCardiologyTransferSummaryNote: C80DocTypecodes_PediatricCardiologyTransferSummaryNote,
  PediatricCardiologyHospitalConsultNote: C80DocTypecodes_PediatricCardiologyHospitalConsultNote,
  PediatricCardiologyHospitalLetter: C80DocTypecodes_PediatricCardiologyHospitalLetter,
  PediatricCriticalCareMedicineHospitalProcedureNote: C80DocTypecodes_PediatricCriticalCareMedicineHospitalProcedureNote,
  PediatricDermatologyHistoryAndPhysicalNote: C80DocTypecodes_PediatricDermatologyHistoryAndPhysicalNote,
  PediatricDermatologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricDermatologyPreoperativeEvaluationAndManagementNote,
  PediatricEndocrinologyDischargeSummary: C80DocTypecodes_PediatricEndocrinologyDischargeSummary,
  PediatricEndocrinologyHistoryAndPhysicalNote: C80DocTypecodes_PediatricEndocrinologyHistoryAndPhysicalNote,
  PediatricEndocrinologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricEndocrinologyPreoperativeEvaluationAndManagementNote,
  PediatricEndocrinologyTransferSummaryNote: C80DocTypecodes_PediatricEndocrinologyTransferSummaryNote,
  PediatricGastroenterologyDischargeSummary: C80DocTypecodes_PediatricGastroenterologyDischargeSummary,
  PediatricGastroenterologyHistoryAndPhysicalNote: C80DocTypecodes_PediatricGastroenterologyHistoryAndPhysicalNote,
  PediatricGastroenterologyNote: C80DocTypecodes_PediatricGastroenterologyNote,
  PediatricGastroenterologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricGastroenterologyPreoperativeEvaluationAndManagementNote,
  PediatricGastroenterologyProcedureNote: C80DocTypecodes_PediatricGastroenterologyProcedureNote,
  PediatricGastroenterologyProgressNote: C80DocTypecodes_PediatricGastroenterologyProgressNote,
  PediatricGastroenterologyTransferSummaryNote: C80DocTypecodes_PediatricGastroenterologyTransferSummaryNote,
  PediatricGastroenterologyHospitalConsultNote: C80DocTypecodes_PediatricGastroenterologyHospitalConsultNote,
  PediatricGastroenterologyHospitalLetter: C80DocTypecodes_PediatricGastroenterologyHospitalLetter,
  PediatricHematologyOncologyDiagnosticStudyNote: C80DocTypecodes_PediatricHematologyOncologyDiagnosticStudyNote,
  PediatricHematologyOncologyDischargeSummary: C80DocTypecodes_PediatricHematologyOncologyDischargeSummary,
  PediatricHematologyOncologyHistoryAndPhysicalNote: C80DocTypecodes_PediatricHematologyOncologyHistoryAndPhysicalNote,
  PediatricHematologyOncologyNote: C80DocTypecodes_PediatricHematologyOncologyNote,
  PediatricHematologyOncologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricHematologyOncologyPreoperativeEvaluationAndManagementNote,
  PediatricHematologyOncologyProcedureNote: C80DocTypecodes_PediatricHematologyOncologyProcedureNote,
  PediatricHematologyOncologyProgressNote: C80DocTypecodes_PediatricHematologyOncologyProgressNote,
  PediatricHematologyOncologyTransferSummaryNote: C80DocTypecodes_PediatricHematologyOncologyTransferSummaryNote,
  PediatricHematologyOncologyHospitalConsultNote: C80DocTypecodes_PediatricHematologyOncologyHospitalConsultNote,
  PediatricHematologyOncologyHospitalLetter: C80DocTypecodes_PediatricHematologyOncologyHospitalLetter,
  PediatricInfectiousDiseasesHistoryAndPhysicalNote: C80DocTypecodes_PediatricInfectiousDiseasesHistoryAndPhysicalNote,
  PediatricInfectiousDiseasesNote: C80DocTypecodes_PediatricInfectiousDiseasesNote,
  PediatricInfectiousDiseasesPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricInfectiousDiseasesPreoperativeEvaluationAndManagementNote,
  PediatricInfectiousDiseasesProgressNote: C80DocTypecodes_PediatricInfectiousDiseasesProgressNote,
  PediatricInfectiousDiseasesTransferSummaryNote: C80DocTypecodes_PediatricInfectiousDiseasesTransferSummaryNote,
  PediatricInfectiousDiseasesHospitalConsultNote: C80DocTypecodes_PediatricInfectiousDiseasesHospitalConsultNote,
  PediatricInfectiousDiseasesHospitalLetter: C80DocTypecodes_PediatricInfectiousDiseasesHospitalLetter,
  PediatricNephrologyDiagnosticStudyNote: C80DocTypecodes_PediatricNephrologyDiagnosticStudyNote,
  PediatricNephrologyDischargeSummary: C80DocTypecodes_PediatricNephrologyDischargeSummary,
  PediatricNephrologyHistoryAndPhysicalNote: C80DocTypecodes_PediatricNephrologyHistoryAndPhysicalNote,
  PediatricNephrologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricNephrologyPreoperativeEvaluationAndManagementNote,
  PediatricNephrologyTransferSummaryNote: C80DocTypecodes_PediatricNephrologyTransferSummaryNote,
  PediatricOtolaryngologyDischargeSummary: C80DocTypecodes_PediatricOtolaryngologyDischargeSummary,
  PediatricOtolaryngologyHistoryAndPhysicalNote: C80DocTypecodes_PediatricOtolaryngologyHistoryAndPhysicalNote,
  PediatricOtolaryngologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricOtolaryngologyPreoperativeEvaluationAndManagementNote,
  PediatricOtolaryngologyTransferSummaryNote: C80DocTypecodes_PediatricOtolaryngologyTransferSummaryNote,
  PediatricPulmonologyDiagnosticStudyNote: C80DocTypecodes_PediatricPulmonologyDiagnosticStudyNote,
  PediatricPulmonologyDischargeSummary: C80DocTypecodes_PediatricPulmonologyDischargeSummary,
  PediatricPulmonologyHistoryAndPhysicalNote: C80DocTypecodes_PediatricPulmonologyHistoryAndPhysicalNote,
  PediatricPulmonologyNote: C80DocTypecodes_PediatricPulmonologyNote,
  PediatricPulmonologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricPulmonologyPreoperativeEvaluationAndManagementNote,
  PediatricPulmonologyProcedureNote: C80DocTypecodes_PediatricPulmonologyProcedureNote,
  PediatricPulmonologyProgressNote: C80DocTypecodes_PediatricPulmonologyProgressNote,
  PediatricPulmonologyTransferSummaryNote: C80DocTypecodes_PediatricPulmonologyTransferSummaryNote,
  PediatricPulmonologyHospitalConsultNote: C80DocTypecodes_PediatricPulmonologyHospitalConsultNote,
  PediatricPulmonologyHospitalDiagnosticStudyNote: C80DocTypecodes_PediatricPulmonologyHospitalDiagnosticStudyNote,
  PediatricPulmonologyHospitalLetter: C80DocTypecodes_PediatricPulmonologyHospitalLetter,
  PediatricRheumatologyHistoryAndPhysicalNote: C80DocTypecodes_PediatricRheumatologyHistoryAndPhysicalNote,
  PediatricRheumatologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricRheumatologyPreoperativeEvaluationAndManagementNote,
  PediatricRheumatologyTransferSummaryNote: C80DocTypecodes_PediatricRheumatologyTransferSummaryNote,
  PediatricSurgeryDiagnosticStudyNote: C80DocTypecodes_PediatricSurgeryDiagnosticStudyNote,
  PediatricSurgeryDischargeSummary: C80DocTypecodes_PediatricSurgeryDischargeSummary,
  PediatricSurgeryHistoryAndPhysicalNote: C80DocTypecodes_PediatricSurgeryHistoryAndPhysicalNote,
  PediatricSurgeryPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricSurgeryPreoperativeEvaluationAndManagementNote,
  PediatricSurgeryProcedureNote: C80DocTypecodes_PediatricSurgeryProcedureNote,
  PediatricSurgeryProgressNote: C80DocTypecodes_PediatricSurgeryProgressNote,
  PediatricSurgeryTransferSummaryNote: C80DocTypecodes_PediatricSurgeryTransferSummaryNote,
  PediatricSurgeryHospitalConsultNote: C80DocTypecodes_PediatricSurgeryHospitalConsultNote,
  PediatricSurgeryHospitalLetter: C80DocTypecodes_PediatricSurgeryHospitalLetter,
  PediatricUrologyDiagnosticStudyNote: C80DocTypecodes_PediatricUrologyDiagnosticStudyNote,
  PediatricUrologyDischargeSummary: C80DocTypecodes_PediatricUrologyDischargeSummary,
  PediatricUrologyHistoryAndPhysicalNote: C80DocTypecodes_PediatricUrologyHistoryAndPhysicalNote,
  PediatricUrologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricUrologyPreoperativeEvaluationAndManagementNote,
  PediatricUrologyProcedureNote: C80DocTypecodes_PediatricUrologyProcedureNote,
  PediatricUrologyProgressNote: C80DocTypecodes_PediatricUrologyProgressNote,
  PediatricUrologyTransferSummaryNote: C80DocTypecodes_PediatricUrologyTransferSummaryNote,
  PediatricUrologyHospitalConsultNote: C80DocTypecodes_PediatricUrologyHospitalConsultNote,
  PediatricUrologyHospitalLetter: C80DocTypecodes_PediatricUrologyHospitalLetter,
  PediatricsAssessmentNote: C80DocTypecodes_PediatricsAssessmentNote,
  PediatricsDischargeSummary: C80DocTypecodes_PediatricsDischargeSummary,
  PediatricsHistoryAndPhysicalNote: C80DocTypecodes_PediatricsHistoryAndPhysicalNote,
  PediatricsNote: C80DocTypecodes_PediatricsNote,
  PediatricsPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricsPreoperativeEvaluationAndManagementNote,
  PediatricsProcedureNote: C80DocTypecodes_PediatricsProcedureNote,
  PediatricsHospitalConsultNote: C80DocTypecodes_PediatricsHospitalConsultNote,
  PediatricsHospitalDiagnosticStudyNote: C80DocTypecodes_PediatricsHospitalDiagnosticStudyNote,
  PediatricsHospitalDischargeSummary: C80DocTypecodes_PediatricsHospitalDischargeSummary,
  PediatricsHospitalHistoryAndPhysicalNote: C80DocTypecodes_PediatricsHospitalHistoryAndPhysicalNote,
  PediatricsHospitalLetter: C80DocTypecodes_PediatricsHospitalLetter,
  PediatricsHospitalNote: C80DocTypecodes_PediatricsHospitalNote,
  PediatricsHospitalPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricsHospitalPreoperativeEvaluationAndManagementNote,
  PediatricsHospitalProcedureNote: C80DocTypecodes_PediatricsHospitalProcedureNote,
  PediatricsHospitalProgressNote: C80DocTypecodes_PediatricsHospitalProgressNote,
  PrimaryCareDischargeSummary: C80DocTypecodes_PrimaryCareDischargeSummary,
  PrimaryCareHistoryAndPhysicalNote: C80DocTypecodes_PrimaryCareHistoryAndPhysicalNote,
  PrimaryCareNote: C80DocTypecodes_PrimaryCareNote,
  PrimaryCarePreoperativeEvaluationAndManagementNote: C80DocTypecodes_PrimaryCarePreoperativeEvaluationAndManagementNote,
  PrimaryCareProcedureNote: C80DocTypecodes_PrimaryCareProcedureNote,
  PrimaryCareHospitalConsultNote: C80DocTypecodes_PrimaryCareHospitalConsultNote,
  PrimaryCareHospitalLetter: C80DocTypecodes_PrimaryCareHospitalLetter,
  ResearchNote: C80DocTypecodes_ResearchNote,
  ResearchProgressNote: C80DocTypecodes_ResearchProgressNote,
  SpeechLanguagePathologyDischargeSummary: C80DocTypecodes_SpeechLanguagePathologyDischargeSummary,
  SpeechLanguagePathologyHistoryAndPhysicalNote: C80DocTypecodes_SpeechLanguagePathologyHistoryAndPhysicalNote,
  SpeechLanguagePathologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_SpeechLanguagePathologyPreoperativeEvaluationAndManagementNote,
  SpeechLanguagePathologyHospitalConsultNote: C80DocTypecodes_SpeechLanguagePathologyHospitalConsultNote,
  SpeechLanguagePathologyHospitalLetter: C80DocTypecodes_SpeechLanguagePathologyHospitalLetter,
  TransplantSurgeryNote: C80DocTypecodes_TransplantSurgeryNote,
  TransplantSurgeryHistoryAndPhysicalNote: C80DocTypecodes_TransplantSurgeryHistoryAndPhysicalNote,
  TransplantSurgeryPreoperativeEvaluationAndManagementNote: C80DocTypecodes_TransplantSurgeryPreoperativeEvaluationAndManagementNote,
  TransplantSurgeryProcedureNote: C80DocTypecodes_TransplantSurgeryProcedureNote,
  TransplantSurgeryHospitalConsultNote: C80DocTypecodes_TransplantSurgeryHospitalConsultNote,
  TransplantSurgeryHospitalLetter: C80DocTypecodes_TransplantSurgeryHospitalLetter,
  PediatricRehabilitationMedicineNote: C80DocTypecodes_PediatricRehabilitationMedicineNote,
  PediatricTransplantHepatologyDiagnosticStudyNote: C80DocTypecodes_PediatricTransplantHepatologyDiagnosticStudyNote,
  PediatricTransplantHepatologyDischargeSummary: C80DocTypecodes_PediatricTransplantHepatologyDischargeSummary,
  PediatricTransplantHepatologyHistoryAndPhysicalNote: C80DocTypecodes_PediatricTransplantHepatologyHistoryAndPhysicalNote,
  PediatricTransplantHepatologyNote: C80DocTypecodes_PediatricTransplantHepatologyNote,
  PediatricTransplantHepatologyPreoperativeEvaluationAndManagementNote: C80DocTypecodes_PediatricTransplantHepatologyPreoperativeEvaluationAndManagementNote,
  PediatricTransplantHepatologyProcedureNote: C80DocTypecodes_PediatricTransplantHepatologyProcedureNote,
  PediatricTransplantHepatologyProgressNote: C80DocTypecodes_PediatricTransplantHepatologyProgressNote,
  PediatricTransplantHepatologyTransferSummaryNote: C80DocTypecodes_PediatricTransplantHepatologyTransferSummaryNote,
  PediatricTransplantHepatologyHospitalConsultNote: C80DocTypecodes_PediatricTransplantHepatologyHospitalConsultNote,
  PediatricTransplantHepatologyHospitalLetter: C80DocTypecodes_PediatricTransplantHepatologyHospitalLetter,
  PediatricNephrologyLetter: C80DocTypecodes_PediatricNephrologyLetter,
  PediatricNephrologyNote: C80DocTypecodes_PediatricNephrologyNote,
  PediatricNephrologyProcedureNote: C80DocTypecodes_PediatricNephrologyProcedureNote,
  PediatricNephrologyHospitalConsultNote: C80DocTypecodes_PediatricNephrologyHospitalConsultNote,
  PediatricNephrologyHospitalLetter: C80DocTypecodes_PediatricNephrologyHospitalLetter,
  PediatricOtolaryngologyNote: C80DocTypecodes_PediatricOtolaryngologyNote,
  PediatricOtolaryngologyProcedureNote: C80DocTypecodes_PediatricOtolaryngologyProcedureNote,
  PediatricOtolaryngologyProgressNote: C80DocTypecodes_PediatricOtolaryngologyProgressNote,
  PediatricOtolaryngologyHospitalConsultNote: C80DocTypecodes_PediatricOtolaryngologyHospitalConsultNote,
  PediatricOtolaryngologyHospitalLetter: C80DocTypecodes_PediatricOtolaryngologyHospitalLetter,
  PediatricRheumatologyNote: C80DocTypecodes_PediatricRheumatologyNote,
  PediatricRheumatologyProcedureNote: C80DocTypecodes_PediatricRheumatologyProcedureNote,
  PediatricRheumatologyProgressNote: C80DocTypecodes_PediatricRheumatologyProgressNote,
  PediatricRheumatologyHospitalConsultNote: C80DocTypecodes_PediatricRheumatologyHospitalConsultNote,
  PediatricRheumatologyHospitalLetter: C80DocTypecodes_PediatricRheumatologyHospitalLetter,
  PediatricSurgeryNote: C80DocTypecodes_PediatricSurgeryNote,
  PediatricUrologyNote: C80DocTypecodes_PediatricUrologyNote,
  PediatricsTransferSummaryNote: C80DocTypecodes_PediatricsTransferSummaryNote,
  PediatricsHospitalTransferSummaryNote: C80DocTypecodes_PediatricsHospitalTransferSummaryNote,
  OphthalmologyTransferSummaryNote: C80DocTypecodes_OphthalmologyTransferSummaryNote,
  PediatricDermatologyNote: C80DocTypecodes_PediatricDermatologyNote,
  PediatricDermatologyProcedureNote: C80DocTypecodes_PediatricDermatologyProcedureNote,
  PediatricDermatologyProgressNote: C80DocTypecodes_PediatricDermatologyProgressNote,
  PediatricDermatologyHospitalConsultNote: C80DocTypecodes_PediatricDermatologyHospitalConsultNote,
  PediatricDermatologyHospitalLetter: C80DocTypecodes_PediatricDermatologyHospitalLetter,
  PediatricEndocrinologyNote: C80DocTypecodes_PediatricEndocrinologyNote,
  PediatricEndocrinologyProcedureNote: C80DocTypecodes_PediatricEndocrinologyProcedureNote,
  PediatricEndocrinologyProgressNote: C80DocTypecodes_PediatricEndocrinologyProgressNote,
  PediatricEndocrinologyHospitalConsultNote: C80DocTypecodes_PediatricEndocrinologyHospitalConsultNote,
  PediatricEndocrinologyHospitalLetter: C80DocTypecodes_PediatricEndocrinologyHospitalLetter,
  AorticArchFluoroscopicAngiogramWContrastIA: C80DocTypecodes_AorticArchFluoroscopicAngiogramWContrastIA,
  AcromioclavicularJointBilateralXRayWOWeight: C80DocTypecodes_AcromioclavicularJointBilateralXRayWOWeight,
  ElbowBilateralXRayAndObliques: C80DocTypecodes_ElbowBilateralXRayAndObliques,
  HandBilateralXRayAPAndLateralAndOblique: C80DocTypecodes_HandBilateralXRayAPAndLateralAndOblique,
  HipBilateralXRay2Views: C80DocTypecodes_HipBilateralXRay2Views,
  HipBilateralXRayAndLateralCrosstable: C80DocTypecodes_HipBilateralXRayAndLateralCrosstable,
  KneeBilateralXRay2ViewsAndSunrise: C80DocTypecodes_KneeBilateralXRay2ViewsAndSunrise,
  KneeBilateralXRay2ViewsAndTunnel: C80DocTypecodes_KneeBilateralXRay2ViewsAndTunnel,
  KneeBilateralXRay4ViewsStanding: C80DocTypecodes_KneeBilateralXRay4ViewsStanding,
  KneeBilateralXRay4ViewsAndSunriseAndTunnel: C80DocTypecodes_KneeBilateralXRay4ViewsAndSunriseAndTunnel,
  KneeBilateralXRaySunriseAndViewsStanding: C80DocTypecodes_KneeBilateralXRaySunriseAndViewsStanding,
  AbdomenXRayAPAndLateralCrosstable: C80DocTypecodes_AbdomenXRayAPAndLateralCrosstable,
  AbdominalVesselsFluoroscopicAngiogramWContrastIV: C80DocTypecodes_AbdominalVesselsFluoroscopicAngiogramWContrastIV,
  UnspecifiedBodyRegionFluoroscopicAngiogramAngioplastyWContrast: C80DocTypecodes_UnspecifiedBodyRegionFluoroscopicAngiogramAngioplastyWContrast,
  MammogramGuidanceForNeedleLocalizationOfBreastBilateral: C80DocTypecodes_MammogramGuidanceForNeedleLocalizationOfBreastBilateral,
  PatellaBilateralXRaySunrise: C80DocTypecodes_PatellaBilateralXRaySunrise,
  RibsBilateralXRayAnteriorAndLateral: C80DocTypecodes_RibsBilateralXRayAnteriorAndLateral,
  RibsBilateralAndChestXRay: C80DocTypecodes_RibsBilateralAndChestXRay,
  WristBilateralXRayUlnarDeviationAndRadialDeviation: C80DocTypecodes_WristBilateralXRayUlnarDeviationAndRadialDeviation,
  FluoroscopyGuidanceForCoreNeedleBiopsyOfUnspecifiedBodyRegion: C80DocTypecodes_FluoroscopyGuidanceForCoreNeedleBiopsyOfUnspecifiedBodyRegion,
  FluoroscopyGuidanceForBiopsyOfPelvis: C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfPelvis,
  FluoroscopyGuidanceForBiopsyOfSalivaryGland: C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfSalivaryGland,
  FluoroscopyGuidanceForBiopsyOfBone: C80DocTypecodes_FluoroscopyGuidanceForBiopsyOfBone,
  BrachiocephalicArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_BrachiocephalicArteryFluoroscopicAngiogramWContrastIA,
  FluoroscopyGuidanceForDrainageOfChest: C80DocTypecodes_FluoroscopyGuidanceForDrainageOfChest,
  ClavicleXRay45DegreeCephalicAngle: C80DocTypecodes_ClavicleXRay45DegreeCephalicAngle,
  SpineCervicalXRay5ViewsWFlexionAndWExtension: C80DocTypecodes_SpineCervicalXRay5ViewsWFlexionAndWExtension,
  SpineCervicalXRay5ViewsAndSwimmers: C80DocTypecodes_SpineCervicalXRay5ViewsAndSwimmers,
  HeadCTAnd3DReconstructionWOContrast: C80DocTypecodes_HeadCTAnd3DReconstructionWOContrast,
  CTGuidanceForBiopsyOfAbdomenWOContrast: C80DocTypecodes_CTGuidanceForBiopsyOfAbdomenWOContrast,
  ChestVesselsCTAngiogramWOContrast: C80DocTypecodes_ChestVesselsCTAngiogramWOContrast,
  RenalVesselsCTAngiogramWAndWOContrast: C80DocTypecodes_RenalVesselsCTAngiogramWAndWOContrast,
  AortaCTWAndWOContrast: C80DocTypecodes_AortaCTWAndWOContrast,
  AnkleBilateralCTWOContrast: C80DocTypecodes_AnkleBilateralCTWOContrast,
  KneeBilateralCTWContrastIV: C80DocTypecodes_KneeBilateralCTWContrastIV,
  KneeBilateralCTWOContrast: C80DocTypecodes_KneeBilateralCTWOContrast,
  ShoulderBilateralCTWOContrast: C80DocTypecodes_ShoulderBilateralCTWOContrast,
  WristBilateralCTWContrastIV: C80DocTypecodes_WristBilateralCTWContrastIV,
  CTGuidanceForBiopsyOfLiverWOContrast: C80DocTypecodes_CTGuidanceForBiopsyOfLiverWOContrast,
  CTGuidanceForBiopsyOfPelvisWContrastIV: C80DocTypecodes_CTGuidanceForBiopsyOfPelvisWContrastIV,
  CTGuidanceForBiopsyOfPelvisWOContrast: C80DocTypecodes_CTGuidanceForBiopsyOfPelvisWOContrast,
  BladderCTWContrastIV: C80DocTypecodes_BladderCTWContrastIV,
  ChestCTLimitedWContrastIV: C80DocTypecodes_ChestCTLimitedWContrastIV,
  CTGuidanceForNeedleBiopsyOfLiver: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfLiver,
  CTGuidanceForNeedleBiopsyOfMuscle: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfMuscle,
  CTGuidanceForNeedleBiopsyOfChestPleura: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfChestPleura,
  CTGuidanceForNeedleBiopsyOfSalivaryGland: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfSalivaryGland,
  CTGuidanceForNeedleBiopsyOfThyroid: C80DocTypecodes_CTGuidanceForNeedleBiopsyOfThyroid,
  AnkleLeftCTWContrastIntraarticular: C80DocTypecodes_AnkleLeftCTWContrastIntraarticular,
  ElbowLeftCTWContrastIntraarticular: C80DocTypecodes_ElbowLeftCTWContrastIntraarticular,
  ExtremityLeftCTWOContrast: C80DocTypecodes_ExtremityLeftCTWOContrast,
  HipLeftCTWContrastIntraarticular: C80DocTypecodes_HipLeftCTWContrastIntraarticular,
  KneeLeftCTWContrastIntraarticular: C80DocTypecodes_KneeLeftCTWContrastIntraarticular,
  WristLeftCTWContrastIntraarticular: C80DocTypecodes_WristLeftCTWContrastIntraarticular,
  PulmonaryVesselsCTAngiogramWAndWOContrast: C80DocTypecodes_PulmonaryVesselsCTAngiogramWAndWOContrast,
  AnkleRightCTWContrastIntraarticular: C80DocTypecodes_AnkleRightCTWContrastIntraarticular,
  ElbowRightCTWContrastIntraarticular: C80DocTypecodes_ElbowRightCTWContrastIntraarticular,
  ExtremityRightCTWOContrast: C80DocTypecodes_ExtremityRightCTWOContrast,
  HipRightCTWContrastIntraarticular: C80DocTypecodes_HipRightCTWContrastIntraarticular,
  KidneyRightCT: C80DocTypecodes_KidneyRightCT,
  KneeRightCTWContrastIntraarticular: C80DocTypecodes_KneeRightCTWContrastIntraarticular,
  WristRightCTWContrastIntraarticular: C80DocTypecodes_WristRightCTWContrastIntraarticular,
  SacrumAndCoccyxCT: C80DocTypecodes_SacrumAndCoccyxCT,
  ScapulaCT: C80DocTypecodes_ScapulaCT,
  ScapulaCTWOContrast: C80DocTypecodes_ScapulaCTWOContrast,
  AortaThoracicCTAngiogramWOContrast: C80DocTypecodes_AortaThoracicCTAngiogramWOContrast,
  FluoroscopyGuidanceForAbscessDrainageOfNeck: C80DocTypecodes_FluoroscopyGuidanceForAbscessDrainageOfNeck,
  FluoroscopyGuidanceForAspirationOfCystOfOvary: C80DocTypecodes_FluoroscopyGuidanceForAspirationOfCystOfOvary,
  FluoroscopyGuidanceForAbscessDrainageOfPancreas: C80DocTypecodes_FluoroscopyGuidanceForAbscessDrainageOfPancreas,
  FluoroscopyGuidanceForAbscessDrainageOfPleuralSpace: C80DocTypecodes_FluoroscopyGuidanceForAbscessDrainageOfPleuralSpace,
  FluoroscopyGuidanceForFineNeedleAspirationOfSuperficialTissue: C80DocTypecodes_FluoroscopyGuidanceForFineNeedleAspirationOfSuperficialTissue,
  FluoroscopyGuidanceForNeedleBiopsyOfLiver: C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfLiver,
  FluoroscopyGuidanceForNeedleBiopsyOfPancreas: C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfPancreas,
  FluoroscopyGuidanceForNeedleBiopsyOfChestPleura: C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfChestPleura,
  FluoroscopyGuidanceForNeedleBiopsyOfSalivaryGland: C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfSalivaryGland,
  FluoroscopyGuidanceForNeedleBiopsyOfThyroid: C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfThyroid,
  HandXRayAPAndLateral: C80DocTypecodes_HandXRayAPAndLateral,
  HipXRayAndDaneliusMiller: C80DocTypecodes_HipXRayAndDaneliusMiller,
  HipXRayDaneliusMiller: C80DocTypecodes_HipXRayDaneliusMiller,
  FluoroscopyGuidanceForDrainageOfHip: C80DocTypecodes_FluoroscopyGuidanceForDrainageOfHip,
  FluoroscopicAngiogramGuidanceForPlacementOfStentInIliacArtery: C80DocTypecodes_FluoroscopicAngiogramGuidanceForPlacementOfStentInIliacArtery,
  IliacArteryFluoroscopicAngiogramAtherectomyWContrast: C80DocTypecodes_IliacArteryFluoroscopicAngiogramAtherectomyWContrast,
  KneeXRaySunriseAndTunnel: C80DocTypecodes_KneeXRaySunriseAndTunnel,
  AnkleLeftXRayAPAndLateralAndObliqueStanding: C80DocTypecodes_AnkleLeftXRayAPAndLateralAndObliqueStanding,
  AnkleLeftXRay3ViewsStanding: C80DocTypecodes_AnkleLeftXRay3ViewsStanding,
  HipLeftXRayAndLateralCrosstable: C80DocTypecodes_HipLeftXRayAndLateralCrosstable,
  HipLeftXRayAndDaneliusMiller: C80DocTypecodes_HipLeftXRayAndDaneliusMiller,
  HipLeftXRayDaneliusMiller: C80DocTypecodes_HipLeftXRayDaneliusMiller,
  KneeLeftXRay2ViewsAndSunrise: C80DocTypecodes_KneeLeftXRay2ViewsAndSunrise,
  KneeLeftXRay2ViewsAndTunnelStanding: C80DocTypecodes_KneeLeftXRay2ViewsAndTunnelStanding,
  KneeLeftXRay4ViewsAndAPStanding: C80DocTypecodes_KneeLeftXRay4ViewsAndAPStanding,
  KneeLeftXRay4ViewsAndTunnel: C80DocTypecodes_KneeLeftXRay4ViewsAndTunnel,
  KneeLeftXRayAPAndLateralCrosstable: C80DocTypecodes_KneeLeftXRayAPAndLateralCrosstable,
  KneeLeftXRayAPAndLateralAndRightObliqueAndLeftOblique: C80DocTypecodes_KneeLeftXRayAPAndLateralAndRightObliqueAndLeftOblique,
  KneeLeftXRayAndTunnel: C80DocTypecodes_KneeLeftXRayAndTunnel,
  KneeLeftXRaySunriseAndViewsStanding: C80DocTypecodes_KneeLeftXRaySunriseAndViewsStanding,
  BreastImplantLeftMammogramDiagnostic: C80DocTypecodes_BreastImplantLeftMammogramDiagnostic,
  WristLeftXRay3ViewsScaphoid: C80DocTypecodes_WristLeftXRay3ViewsScaphoid,
  PatellaLeftXRaySingleView: C80DocTypecodes_PatellaLeftXRaySingleView,
  ShoulderLeftXRayAPAndGrasheyAndAxillary: C80DocTypecodes_ShoulderLeftXRayAPAndGrasheyAndAxillary,
  ShoulderLeftXRay3ViewsAndAxillary: C80DocTypecodes_ShoulderLeftXRay3ViewsAndAxillary,
  ShoulderLeftXRay3ViewsAndY: C80DocTypecodes_ShoulderLeftXRay3ViewsAndY,
  ShoulderLeftXRayGrasheyAndY: C80DocTypecodes_ShoulderLeftXRayGrasheyAndY,
  WristLeftXRayLateralWFlexionAndWExtension: C80DocTypecodes_WristLeftXRayLateralWFlexionAndWExtension,
  BreastImplantXRayDiagnostic: C80DocTypecodes_BreastImplantXRayDiagnostic,
  BreastImplantXRayScreening: C80DocTypecodes_BreastImplantXRayScreening,
  MammogramGuidanceStereotacticForNeedleBiopsyOfBreast: C80DocTypecodes_MammogramGuidanceStereotacticForNeedleBiopsyOfBreast,
  CircleOfWillisMRIAngiogramWAndWOContrastIV: C80DocTypecodes_CircleOfWillisMRIAngiogramWAndWOContrastIV,
  PulmonaryArteryBilateralMRIAngiogramWContrastIA: C80DocTypecodes_PulmonaryArteryBilateralMRIAngiogramWContrastIA,
  AnkleBilateralMRIWContrastIV: C80DocTypecodes_AnkleBilateralMRIWContrastIV,
  AnkleBilateralMRIWOContrast: C80DocTypecodes_AnkleBilateralMRIWOContrast,
  BreastImplantBilateralMRI: C80DocTypecodes_BreastImplantBilateralMRI,
  BreastImplantBilateralMRIWAndWOContrastIV: C80DocTypecodes_BreastImplantBilateralMRIWAndWOContrastIV,
  BreastImplantBilateralMRIWContrastIV: C80DocTypecodes_BreastImplantBilateralMRIWContrastIV,
  BreastImplantBilateralMRIWOContrast: C80DocTypecodes_BreastImplantBilateralMRIWOContrast,
  MRIGuidanceForBiopsyOfBreastBilateral: C80DocTypecodes_MRIGuidanceForBiopsyOfBreastBilateral,
  ElbowBilateralMRIWContrastIV: C80DocTypecodes_ElbowBilateralMRIWContrastIV,
  ElbowBilateralMRIWOContrast: C80DocTypecodes_ElbowBilateralMRIWOContrast,
  FemurBilateralMRIWContrastIV: C80DocTypecodes_FemurBilateralMRIWContrastIV,
  FemurBilateralMRIWOContrast: C80DocTypecodes_FemurBilateralMRIWOContrast,
  ForearmBilateralMRIWAndWOContrastIV: C80DocTypecodes_ForearmBilateralMRIWAndWOContrastIV,
  ForearmBilateralMRIWContrastIV: C80DocTypecodes_ForearmBilateralMRIWContrastIV,
  ForearmBilateralMRIWOContrast: C80DocTypecodes_ForearmBilateralMRIWOContrast,
  HandBilateralMRIWAndWOContrastIV: C80DocTypecodes_HandBilateralMRIWAndWOContrastIV,
  HandBilateralMRIWContrastIV: C80DocTypecodes_HandBilateralMRIWContrastIV,
  HandBilateralMRIWOContrast: C80DocTypecodes_HandBilateralMRIWOContrast,
  UpperArmBilateralMRI: C80DocTypecodes_UpperArmBilateralMRI,
  UpperArmBilateralMRIWAndWOContrastIV: C80DocTypecodes_UpperArmBilateralMRIWAndWOContrastIV,
  UpperArmBilateralMRIWContrastIV: C80DocTypecodes_UpperArmBilateralMRIWContrastIV,
  UpperArmBilateralMRIWOContrast: C80DocTypecodes_UpperArmBilateralMRIWOContrast,
  ShoulderBilateralMRIWContrastIV: C80DocTypecodes_ShoulderBilateralMRIWContrastIV,
  LowerLegBilateralMRIWOContrast: C80DocTypecodes_LowerLegBilateralMRIWOContrast,
  UpperExtremityBilateralMRIWAndWOContrastIV: C80DocTypecodes_UpperExtremityBilateralMRIWAndWOContrastIV,
  UpperExtremityBilateralMRIWContrastIV: C80DocTypecodes_UpperExtremityBilateralMRIWContrastIV,
  UpperExtremityBilateralMRIWOContrast: C80DocTypecodes_UpperExtremityBilateralMRIWOContrast,
  BreastImplantMRIWAndWOContrastIV: C80DocTypecodes_BreastImplantMRIWAndWOContrastIV,
  BreastImplantMRIWContrastIV: C80DocTypecodes_BreastImplantMRIWContrastIV,
  BreastImplantMRIWOContrast: C80DocTypecodes_BreastImplantMRIWOContrast,
  MRIGuidanceForAspirationOfCystOfBreast: C80DocTypecodes_MRIGuidanceForAspirationOfCystOfBreast,
  ExtremityMRI: C80DocTypecodes_ExtremityMRI,
  FingerMRIWAndWOContrastIV: C80DocTypecodes_FingerMRIWAndWOContrastIV,
  FingerMRIWContrastIV: C80DocTypecodes_FingerMRIWContrastIV,
  FingerMRIWOContrast: C80DocTypecodes_FingerMRIWOContrast,
  MRIGuidanceForNeedleBiopsyOfLiver: C80DocTypecodes_MRIGuidanceForNeedleBiopsyOfLiver,
  MRIGuidanceForNeedleBiopsyOfMuscle: C80DocTypecodes_MRIGuidanceForNeedleBiopsyOfMuscle,
  MRIGuidanceForNeedleBiopsyOfPancreas: C80DocTypecodes_MRIGuidanceForNeedleBiopsyOfPancreas,
  MRIGuidanceForNeedleBiopsyOfPleura: C80DocTypecodes_MRIGuidanceForNeedleBiopsyOfPleura,
  MRIGuidanceForNeedleBiopsyOfSalivaryGland: C80DocTypecodes_MRIGuidanceForNeedleBiopsyOfSalivaryGland,
  MRIGuidanceForNeedleBiopsyOfThyroid: C80DocTypecodes_MRIGuidanceForNeedleBiopsyOfThyroid,
  MRIGuidanceForBiopsyOfBreastLeft: C80DocTypecodes_MRIGuidanceForBiopsyOfBreastLeft,
  FingerLeftMRIWAndWOContrastIV: C80DocTypecodes_FingerLeftMRIWAndWOContrastIV,
  FingerLeftMRIWContrastIV: C80DocTypecodes_FingerLeftMRIWContrastIV,
  FingerLeftMRIWOContrast: C80DocTypecodes_FingerLeftMRIWOContrast,
  HipLeftMRIWAndWOContrastIntraarticular: C80DocTypecodes_HipLeftMRIWAndWOContrastIntraarticular,
  ShoulderLeftMRIWAndWOContrastIntraarticular: C80DocTypecodes_ShoulderLeftMRIWAndWOContrastIntraarticular,
  WristLeftAndHandLeftMRI: C80DocTypecodes_WristLeftAndHandLeftMRI,
  LowerExtremityJointMRIWContrastIntraarticular: C80DocTypecodes_LowerExtremityJointMRIWContrastIntraarticular,
  NasalBonesMRI: C80DocTypecodes_NasalBonesMRI,
  PelvisMRILimited: C80DocTypecodes_PelvisMRILimited,
  MRIGuidanceForBiopsyOfBreastRight: C80DocTypecodes_MRIGuidanceForBiopsyOfBreastRight,
  FingerRightMRIWAndWOContrastIV: C80DocTypecodes_FingerRightMRIWAndWOContrastIV,
  FingerRightMRIWContrastIV: C80DocTypecodes_FingerRightMRIWContrastIV,
  FingerRightMRIWOContrast: C80DocTypecodes_FingerRightMRIWOContrast,
  HipRightMRIWAndWOContrastIntraarticular: C80DocTypecodes_HipRightMRIWAndWOContrastIntraarticular,
  ShoulderRightMRIWAndWOContrastIntraarticular: C80DocTypecodes_ShoulderRightMRIWAndWOContrastIntraarticular,
  WristRightAndHandRightMRI: C80DocTypecodes_WristRightAndHandRightMRI,
  SkullBaseMRIWAndWOContrastIV: C80DocTypecodes_SkullBaseMRIWAndWOContrastIV,
  ScrotumAndTesticleMRIWContrastIV: C80DocTypecodes_ScrotumAndTesticleMRIWContrastIV,
  VenaCavaMRI: C80DocTypecodes_VenaCavaMRI,
  UnspecifiedBodyRegionMRIWOContrast: C80DocTypecodes_UnspecifiedBodyRegionMRIWOContrast,
  FluoroscopyGuidanceForNeedleBiopsyOfAbdomen: C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfAbdomen,
  FluoroscopyGuidanceForNeedleBiopsyOfChest: C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfChest,
  FluoroscopyGuidanceForNeedleBiopsyOfMuscle: C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfMuscle,
  FluoroscopyGuidanceForNeedleBiopsyOfPleura: C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfPleura,
  FluoroscopyGuidanceForNeedleBiopsyOfProstate: C80DocTypecodes_FluoroscopyGuidanceForNeedleBiopsyOfProstate,
  LiverSPECTWTc99mSCIV: C80DocTypecodes_LiverSPECTWTc99mSCIV,
  LiverScanWTc99mSCIV: C80DocTypecodes_LiverScanWTc99mSCIV,
  HeartScanWStressAndWTc99mIV: C80DocTypecodes_HeartScanWStressAndWTc99mIV,
  HeartScanWStressAndWTc99mSestamibiIV: C80DocTypecodes_HeartScanWStressAndWTc99mSestamibiIV,
  ParotidGlandScanWTc99mPertechnetateIV: C80DocTypecodes_ParotidGlandScanWTc99mPertechnetateIV,
  SpleenSPECTWTc99mTaggedRBCIV: C80DocTypecodes_SpleenSPECTWTc99mTaggedRBCIV,
  ScrotumAndTesticleSPECTFlow: C80DocTypecodes_ScrotumAndTesticleSPECTFlow,
  ThyroidScanAndUptakeWI131PO: C80DocTypecodes_ThyroidScanAndUptakeWI131PO,
  SPECTForTumorWholeBody: C80DocTypecodes_SPECTForTumorWholeBody,
  UrinaryBladderAndUrethraSPECTWContrastIntraBladderDuringVoiding: C80DocTypecodes_UrinaryBladderAndUrethraSPECTWContrastIntraBladderDuringVoiding,
  PatellaXRaySunrise: C80DocTypecodes_PatellaXRaySunrise,
  FluoroscopyGuidanceForPercutaneousBiopsyOfAbdomen: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousBiopsyOfAbdomen,
  FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfAbdomen: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfAbdomen,
  FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfAppendix: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfAppendix,
  FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfLung: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfLung,
  FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfPelvis: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousDrainageOfAbscessOfPelvis,
  FluoroscopyGuidanceForPercutaneousNeedleBiopsyOfKidney: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousNeedleBiopsyOfKidney,
  FluoroscopyGuidanceForPercutaneousNeedleBiopsyOfLiver: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousNeedleBiopsyOfLiver,
  FluoroscopyGuidanceForPercutaneousNeedleBiopsyOfSalivaryGland: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousNeedleBiopsyOfSalivaryGland,
  RenalArteryFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIA: C80DocTypecodes_RenalArteryFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIA,
  PoplitealArteryFluoroscopicAngiogramWContrastIA: C80DocTypecodes_PoplitealArteryFluoroscopicAngiogramWContrastIA,
  PortalVeinFluoroscopicAngiogramWContrastIV: C80DocTypecodes_PortalVeinFluoroscopicAngiogramWContrastIV,
  BreastMammogramPostWirePlacement: C80DocTypecodes_BreastMammogramPostWirePlacement,
  PulmonaryArteryFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIA: C80DocTypecodes_PulmonaryArteryFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIA,
  RenalVesselsFluoroscopicAngiogramAtherectomyWContrast: C80DocTypecodes_RenalVesselsFluoroscopicAngiogramAtherectomyWContrast,
  AnkleRightXRay3ViewsStanding: C80DocTypecodes_AnkleRightXRay3ViewsStanding,
  KneeRightXRaySunriseAndTunnelStanding: C80DocTypecodes_KneeRightXRaySunriseAndTunnelStanding,
  KneeRightXRaySunrise: C80DocTypecodes_KneeRightXRaySunrise,
  LowerExtremityRightXRay2Views: C80DocTypecodes_LowerExtremityRightXRay2Views,
  LowerExtremityRightXRayAPAndLateral: C80DocTypecodes_LowerExtremityRightXRayAPAndLateral,
  BreastImplantRightMammogramDiagnostic: C80DocTypecodes_BreastImplantRightMammogramDiagnostic,
  PatellaRightXRaySingleView: C80DocTypecodes_PatellaRightXRaySingleView,
  PatellaRightXRay3Views: C80DocTypecodes_PatellaRightXRay3Views,
  ShoulderRightXRayAPAndGrasheyAndAxillary: C80DocTypecodes_ShoulderRightXRayAPAndGrasheyAndAxillary,
  WristRightXRayPAWClenchedFist: C80DocTypecodes_WristRightXRayPAWClenchedFist,
  SacrumXRayStanding: C80DocTypecodes_SacrumXRayStanding,
  ShoulderXRay4Views: C80DocTypecodes_ShoulderXRay4Views,
  ShoulderXRayAPAndY: C80DocTypecodes_ShoulderXRayAPAndY,
  ShoulderXRayGrasheyAndAxillaryAndY: C80DocTypecodes_ShoulderXRayGrasheyAndAxillaryAndY,
  BreastDuctMammogramSingleViewWContrastIntraDuct: C80DocTypecodes_BreastDuctMammogramSingleViewWContrastIntraDuct,
  SkullXRayAPSingleView: C80DocTypecodes_SkullXRayAPSingleView,
  SkullXRayPA: C80DocTypecodes_SkullXRayPA,
  SkullXRayPAAndLateralAndWatersAndTowne: C80DocTypecodes_SkullXRayPAAndLateralAndWatersAndTowne,
  SmallBowelFluoroscopyWContrastViaIleostomy: C80DocTypecodes_SmallBowelFluoroscopyWContrastViaIleostomy,
  SpineThoracolumbarJunctionXRay2Views: C80DocTypecodes_SpineThoracolumbarJunctionXRay2Views,
  SpineThoracicXRay2ViewsStanding: C80DocTypecodes_SpineThoracicXRay2ViewsStanding,
  SpineThoracicXRayStanding: C80DocTypecodes_SpineThoracicXRayStanding,
  AortaAbdominalUS: C80DocTypecodes_AortaAbdominalUS,
  AdrenalGlandUS: C80DocTypecodes_AdrenalGlandUS,
  USGuidanceForAspirationOfBreastBilateral: C80DocTypecodes_USGuidanceForAspirationOfBreastBilateral,
  USGuidanceForCoreNeedleBiopsyOfLymphNode: C80DocTypecodes_USGuidanceForCoreNeedleBiopsyOfLymphNode,
  BladderUSLimited: C80DocTypecodes_BladderUSLimited,
  ChestUSLimited: C80DocTypecodes_ChestUSLimited,
  UnspecifiedBodyRegionUSDopplerLimited: C80DocTypecodes_UnspecifiedBodyRegionUSDopplerLimited,
  ExtremityVeinsBilateralUSDoppler: C80DocTypecodes_ExtremityVeinsBilateralUSDoppler,
  PortalVeinAndHepaticVeinUSDoppler: C80DocTypecodes_PortalVeinAndHepaticVeinUSDoppler,
  UmbilicalArteryUSDoppler: C80DocTypecodes_UmbilicalArteryUSDoppler,
  EyeUSLimited: C80DocTypecodes_EyeUSLimited,
  USGuidanceForAspirationOfLymphNode: C80DocTypecodes_USGuidanceForAspirationOfLymphNode,
  USGuidanceForNeedleBiopsyOfMuscle: C80DocTypecodes_USGuidanceForNeedleBiopsyOfMuscle,
  USGuidanceForNeedleBiopsyOfPancreas: C80DocTypecodes_USGuidanceForNeedleBiopsyOfPancreas,
  USGuidanceForNeedleBiopsyOfBreastRight: C80DocTypecodes_USGuidanceForNeedleBiopsyOfBreastRight,
  USGuidanceForNeedleBiopsyOfSalivaryGland: C80DocTypecodes_USGuidanceForNeedleBiopsyOfSalivaryGland,
  USGuidanceForAspirationOfBreastLeft: C80DocTypecodes_USGuidanceForAspirationOfBreastLeft,
  ExtremityArteryLeftUS: C80DocTypecodes_ExtremityArteryLeftUS,
  RenalArteryUS: C80DocTypecodes_RenalArteryUS,
  RenalVesselsUS: C80DocTypecodes_RenalVesselsUS,
  USGuidanceForAspirationOfBreastRight: C80DocTypecodes_USGuidanceForAspirationOfBreastRight,
  ExtremityArteryRightUS: C80DocTypecodes_ExtremityArteryRightUS,
  SalivaryGlandUS: C80DocTypecodes_SalivaryGlandUS,
  ScrotumAndTesticleUSLimited: C80DocTypecodes_ScrotumAndTesticleUSLimited,
  KidneyTransplantUSLimited: C80DocTypecodes_KidneyTransplantUSLimited,
  UpperExtremityVeinFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIV: C80DocTypecodes_UpperExtremityVeinFluoroscopicAngiogramPercutaneousTransluminalAngioplastyOfVesselWContrastIV,
  WristXRayWClenchedFist: C80DocTypecodes_WristXRayWClenchedFist,
  WristXRayUlnarDeviationAndRadialDeviation: C80DocTypecodes_WristXRayUlnarDeviationAndRadialDeviation,
  WristXRayUlnarDeviation: C80DocTypecodes_WristXRayUlnarDeviation,
  ZygomaticArchXRay2Views: C80DocTypecodes_ZygomaticArchXRay2Views,
  FluoroscopyGuidanceForAspirationOfCystOfBone: C80DocTypecodes_FluoroscopyGuidanceForAspirationOfCystOfBone,
  AnkleLeftXRaySingleView: C80DocTypecodes_AnkleLeftXRaySingleView,
  ElbowLeftXRaySingleView: C80DocTypecodes_ElbowLeftXRaySingleView,
  FootLeftXRaySingleView: C80DocTypecodes_FootLeftXRaySingleView,
  HandLeftXRaySingleView: C80DocTypecodes_HandLeftXRaySingleView,
  CalcaneusLeftXRaySingleView: C80DocTypecodes_CalcaneusLeftXRaySingleView,
  HumerusLeftXRaySingleView: C80DocTypecodes_HumerusLeftXRaySingleView,
  TibiaLeftAndFibulaLeftXRaySingleView: C80DocTypecodes_TibiaLeftAndFibulaLeftXRaySingleView,
  AnkleRightXRaySingleView: C80DocTypecodes_AnkleRightXRaySingleView,
  ElbowRightXRaySingleView: C80DocTypecodes_ElbowRightXRaySingleView,
  FootRightXRaySingleView: C80DocTypecodes_FootRightXRaySingleView,
  RadiusRightAndUlnaRightXRaySingleView: C80DocTypecodes_RadiusRightAndUlnaRightXRaySingleView,
  HandRightXRaySingleView: C80DocTypecodes_HandRightXRaySingleView,
  CalcaneusRightXRaySingleView: C80DocTypecodes_CalcaneusRightXRaySingleView,
  HumerusRightXRaySingleView: C80DocTypecodes_HumerusRightXRaySingleView,
  TibiaRightAndFibulaRightXRaySingleView: C80DocTypecodes_TibiaRightAndFibulaRightXRaySingleView,
  LowerExtremityVeinsBilateralUS: C80DocTypecodes_LowerExtremityVeinsBilateralUS,
  USGuidanceForBiopsyOfEpididymis: C80DocTypecodes_USGuidanceForBiopsyOfEpididymis,
  UrinaryBladderUSPostVoid: C80DocTypecodes_UrinaryBladderUSPostVoid,
  FemoralArteryAndPoplitealArteryUS: C80DocTypecodes_FemoralArteryAndPoplitealArteryUS,
  OvaryUS: C80DocTypecodes_OvaryUS,
  USGuidanceForCordocentesis: C80DocTypecodes_USGuidanceForCordocentesis,
  LowerExtremityVeinsLeftUS: C80DocTypecodes_LowerExtremityVeinsLeftUS,
  SpineLumbarUS: C80DocTypecodes_SpineLumbarUS,
  MesentericArteryUS: C80DocTypecodes_MesentericArteryUS,
  UpperExtremityVeinsUS: C80DocTypecodes_UpperExtremityVeinsUS,
  USGuidanceForBiopsyOfSpinalCord: C80DocTypecodes_USGuidanceForBiopsyOfSpinalCord,
  BreastVesselsUSDoppler: C80DocTypecodes_BreastVesselsUSDoppler,
  ExtremityVesselsLeftUSDoppler: C80DocTypecodes_ExtremityVesselsLeftUSDoppler,
  FemoralVeinAndPoplitealVeinUS: C80DocTypecodes_FemoralVeinAndPoplitealVeinUS,
  USGuidanceForChorionicVillusSampling: C80DocTypecodes_USGuidanceForChorionicVillusSampling,
  USGuidanceForNeedleBiopsyOfSpinalCord: C80DocTypecodes_USGuidanceForNeedleBiopsyOfSpinalCord,
  KidneyBilateralAndBladderUS: C80DocTypecodes_KidneyBilateralAndBladderUS,
  USStandardCertificateOfDeath2003Revision: C80DocTypecodes_USStandardCertificateOfDeath2003Revision,
  ReferralNoteForensicMedicine: C80DocTypecodes_ReferralNoteForensicMedicine,
  PopulationStratificationDescriptionNarrative: C80DocTypecodes_PopulationStratificationDescriptionNarrative,
  HealthQualityMeasureSupplementalDataNarrative: C80DocTypecodes_HealthQualityMeasureSupplementalDataNarrative,
  FDAProductLabelStatementOfIdentitySection: C80DocTypecodes_FDAProductLabelStatementOfIdentitySection,
  FDAProductLabelHealthClaimSection: C80DocTypecodes_FDAProductLabelHealthClaimSection,
  InstructionsTextNarrative: C80DocTypecodes_InstructionsTextNarrative,
  FDAPackageInsertDiagramOfDevice: C80DocTypecodes_FDAPackageInsertDiagramOfDevice,
  FDAPackageInsertRisks: C80DocTypecodes_FDAPackageInsertRisks,
  FDAPackageInsertCompatibleAccessories: C80DocTypecodes_FDAPackageInsertCompatibleAccessories,
  FDAPackageInsertAlarms: C80DocTypecodes_FDAPackageInsertAlarms,
  FDAPackageInsertTroubleshooting: C80DocTypecodes_FDAPackageInsertTroubleshooting,
  FDAPackageInsertDisposalAndWasteHandling: C80DocTypecodes_FDAPackageInsertDisposalAndWasteHandling,
  DocumentType: C80DocTypecodes_DocumentType,
  VAL21DeoxycorticosteroneMolesVolumeInSerumOrPlasma: C80DocTypecodes_VAL21DeoxycorticosteroneMolesVolumeInSerumOrPlasma,
  AbdominalVesselsAndPelvisVesselsCTAngiogramWContrastIV: C80DocTypecodes_AbdominalVesselsAndPelvisVesselsCTAngiogramWContrastIV,
  AsthmaActionPlan: C80DocTypecodes_AsthmaActionPlan,
  DiagnosticStudyNote: C80DocTypecodes_DiagnosticStudyNote,
  EvaluationAndManagementOfSmokingCessation: C80DocTypecodes_EvaluationAndManagementOfSmokingCessation,
  MedicationManagementNote: C80DocTypecodes_MedicationManagementNote,
  RestraintNote: C80DocTypecodes_RestraintNote,
  TransplantSurgeryHospitalProgressNote: C80DocTypecodes_TransplantSurgeryHospitalProgressNote,
  USGuidanceForCSFAspirationOfSpineCervical: C80DocTypecodes_USGuidanceForCSFAspirationOfSpineCervical,
  USGuidanceForCSFAspirationOfSpineLumbar: C80DocTypecodes_USGuidanceForCSFAspirationOfSpineLumbar,
  USGuidanceForCSFAspirationOfSpineThoracic: C80DocTypecodes_USGuidanceForCSFAspirationOfSpineThoracic,
  FluoroscopyGuidanceForInjectionOfSpineCervical: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineCervical,
  FluoroscopyGuidanceForInjectionOfSpineLumbar: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineLumbar,
  FluoroscopyGuidanceForInjectionOfSpineThoracic: C80DocTypecodes_FluoroscopyGuidanceForInjectionOfSpineThoracic,
  CTGuidanceForNerveBlockOfSpineCervical: C80DocTypecodes_CTGuidanceForNerveBlockOfSpineCervical,
  CTGuidanceForNerveBlockOfSpineThoracic: C80DocTypecodes_CTGuidanceForNerveBlockOfSpineThoracic,
  FluoroscopyGuidanceForPercutaneousVertebroplastyOfSpineCervical: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousVertebroplastyOfSpineCervical,
  FluoroscopyGuidanceForPercutaneousVertebroplastyOfSpineLumbar: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousVertebroplastyOfSpineLumbar,
  FluoroscopyGuidanceForPercutaneousVertebroplastyOfSpineThoracic: C80DocTypecodes_FluoroscopyGuidanceForPercutaneousVertebroplastyOfSpineThoracic,
  SpineCervicalUS: C80DocTypecodes_SpineCervicalUS,
  SpineThoracicUS: C80DocTypecodes_SpineThoracicUS,
  SpineLumbarCTStereotactic: C80DocTypecodes_SpineLumbarCTStereotactic,
  SpineCervicalCTStereotactic: C80DocTypecodes_SpineCervicalCTStereotactic,
  SpineThoracicCTStereotactic: C80DocTypecodes_SpineThoracicCTStereotactic,
  SpineThoracicCTWContrastIntradisc: C80DocTypecodes_SpineThoracicCTWContrastIntradisc,
  SpineThoracicXRaySingleViewPortable: C80DocTypecodes_SpineThoracicXRaySingleViewPortable,
  SpineThoracicFluoroscopyWContrastIntradisc: C80DocTypecodes_SpineThoracicFluoroscopyWContrastIntradisc,
  OcularHistoryNarrative: C80DocTypecodes_OcularHistoryNarrative,
  OphthalmicMedicationsNarrative: C80DocTypecodes_OphthalmicMedicationsNarrative,
  VisionTestingNarrative: C80DocTypecodes_VisionTestingNarrative,
  RefractiveMeasurementsNarrative: C80DocTypecodes_RefractiveMeasurementsNarrative,
  LensometryMeasurementsNarrative: C80DocTypecodes_LensometryMeasurementsNarrative,
  ConfrontationVisualFieldNarrative: C80DocTypecodes_ConfrontationVisualFieldNarrative,
  EyeExternalNarrative: C80DocTypecodes_EyeExternalNarrative,
  OcularAlignmentAndMotilityNarrative: C80DocTypecodes_OcularAlignmentAndMotilityNarrative,
  EyeAnteriorSegmentNarrative: C80DocTypecodes_EyeAnteriorSegmentNarrative,
  EyePosteriorSegmentNarrative: C80DocTypecodes_EyePosteriorSegmentNarrative,
  LacrimalNarrative: C80DocTypecodes_LacrimalNarrative,
  AncillaryEyeTestsNarrative: C80DocTypecodes_AncillaryEyeTestsNarrative,
  OcularPhysicalExamNarrative: C80DocTypecodes_OcularPhysicalExamNarrative,
  PathologyReportSectionHeading: C80DocTypecodes_PathologyReportSectionHeading,
  BirthCertificateDocument: C80DocTypecodes_BirthCertificateDocument,
  CMSPhysicalExamPanel: C80DocTypecodes_CMSPhysicalExamPanel,
  CMSReviewOfSystemsPanel: C80DocTypecodes_CMSReviewOfSystemsPanel,
  CMSPastFamilySocialHistoryPanel: C80DocTypecodes_CMSPastFamilySocialHistoryPanel,
  CMSHistoryOfPresentIllnessPanel: C80DocTypecodes_CMSHistoryOfPresentIllnessPanel,
  FDAPackageInsertIndexingBillingUnit: C80DocTypecodes_FDAPackageInsertIndexingBillingUnit,
  RiskAssessmentDocument: C80DocTypecodes_RiskAssessmentDocument,
  FDAPackageInsertPMICommonSideEffectsSection: C80DocTypecodes_FDAPackageInsertPMICommonSideEffectsSection,
  FDAPackageInsertPMIGetEmergencyMedicalHelpSection: C80DocTypecodes_FDAPackageInsertPMIGetEmergencyMedicalHelpSection,
  FDAPackageInsertPMIStopTakingAndCallYourDoctorSection: C80DocTypecodes_FDAPackageInsertPMIStopTakingAndCallYourDoctorSection,
  FDAPackageInsertPMIDirectionsForUseSection: C80DocTypecodes_FDAPackageInsertPMIDirectionsForUseSection,
  FDAPackageInsertPMITellYourDoctorBeforeTakingSection: C80DocTypecodes_FDAPackageInsertPMITellYourDoctorBeforeTakingSection,
  FDAPackageInsertPMIDoNotTakeSection: C80DocTypecodes_FDAPackageInsertPMIDoNotTakeSection,
  FDAPackageInsertPMIImportantInformationSection: C80DocTypecodes_FDAPackageInsertPMIImportantInformationSection,
  FDAPackageInsertPMIUsesSection: C80DocTypecodes_FDAPackageInsertPMIUsesSection,
  FDAProductLabelGenericDrugFacilityIdentificationSubmission: C80DocTypecodes_FDAProductLabelGenericDrugFacilityIdentificationSubmission,
  FDAPackageInsertHealthCareProviderLetter: C80DocTypecodes_FDAPackageInsertHealthCareProviderLetter,
  FDAProductLabelIdentificationOfCBERRegulatedGenericDrugFacility: C80DocTypecodes_FDAProductLabelIdentificationOfCBERRegulatedGenericDrugFacility,
  CancerEventReport: C80DocTypecodes_CancerEventReport,
  CancerDiagnosisNarrative: C80DocTypecodes_CancerDiagnosisNarrative,
  BreastRightFFDMammogramTomosynthesisDiagnostic: C80DocTypecodes_BreastRightFFDMammogramTomosynthesisDiagnostic,
  BreastLeftFFDMammogramTomosynthesisDiagnostic: C80DocTypecodes_BreastLeftFFDMammogramTomosynthesisDiagnostic,
  BreastBilateralFFDMammogramTomosynthesisDiagnostic: C80DocTypecodes_BreastBilateralFFDMammogramTomosynthesisDiagnostic,
  BreastRightFFDMammogramTomosynthesisScreening: C80DocTypecodes_BreastRightFFDMammogramTomosynthesisScreening,
  BreastLeftFFDMammogramTomosynthesisScreening: C80DocTypecodes_BreastLeftFFDMammogramTomosynthesisScreening,
  BreastBilateralFFDMammogramTomosynthesisScreening: C80DocTypecodes_BreastBilateralFFDMammogramTomosynthesisScreening,
  PermissionToReleaseImmunizationDataFromSchoolRecord: C80DocTypecodes_PermissionToReleaseImmunizationDataFromSchoolRecord,
  PhotographicImageUnspecifiedBodyRegionDocument: C80DocTypecodes_PhotographicImageUnspecifiedBodyRegionDocument,
  ToesRightMRIWAndWOContrastIV: C80DocTypecodes_ToesRightMRIWAndWOContrastIV,
  ToesRightMRIWOContrast: C80DocTypecodes_ToesRightMRIWOContrast,
  ToesRightMRIWContrastIV: C80DocTypecodes_ToesRightMRIWContrastIV,
  ToesLeftMRIWAndWOContrastIV: C80DocTypecodes_ToesLeftMRIWAndWOContrastIV,
  ToesLeftMRIWOContrast: C80DocTypecodes_ToesLeftMRIWOContrast,
  ToesLeftMRIWContrastIV: C80DocTypecodes_ToesLeftMRIWContrastIV,
  PelvisMRIWAndWOContrastIVAndWEndorectalCoil: C80DocTypecodes_PelvisMRIWAndWOContrastIVAndWEndorectalCoil,
  PelvisMRIWContrastPRAtRestAndMaxmalSphincterContractionDuringStrainingAndDefecation: C80DocTypecodes_PelvisMRIWContrastPRAtRestAndMaxmalSphincterContractionDuringStrainingAndDefecation,
  AbdomenAndPelvisMRIWContrastPOAndWAndWOContrastIV: C80DocTypecodes_AbdomenAndPelvisMRIWContrastPOAndWAndWOContrastIV,
  AbdomenAndPelvisMRIWContrastPOAndWOContrastIV: C80DocTypecodes_AbdomenAndPelvisMRIWContrastPOAndWOContrastIV,
  AbdomenMRCPWithAndWithoutContrastIV: C80DocTypecodes_AbdomenMRCPWithAndWithoutContrastIV,
  FacialBonesAndSinusesCTWOContrast: C80DocTypecodes_FacialBonesAndSinusesCTWOContrast,
  AbdomenAndPelvisCTWContrastPOAndWContrastIV: C80DocTypecodes_AbdomenAndPelvisCTWContrastPOAndWContrastIV,
  ChestVesselsCTMultisectionForPulmonaryEmbolus: C80DocTypecodes_ChestVesselsCTMultisectionForPulmonaryEmbolus,
  ChestAndAbdomenAndPelvisCTWAndWOContrastIV: C80DocTypecodes_ChestAndAbdomenAndPelvisCTWAndWOContrastIV,
  ChestAndAbdomenAndPelvisCTWOContrast: C80DocTypecodes_ChestAndAbdomenAndPelvisCTWOContrast,
  ChestAndAbdomenAndPelvisCTWContrastIV: C80DocTypecodes_ChestAndAbdomenAndPelvisCTWContrastIV,
  AortaAndFemoralArteryBilateralCTAngiogramWContrastIV: C80DocTypecodes_AortaAndFemoralArteryBilateralCTAngiogramWContrastIV,
  AbdomenXRayForMotilityWithRadioopaqueMarkers: C80DocTypecodes_AbdomenXRayForMotilityWithRadioopaqueMarkers,
  EvaluationOfMentalAndPhysicalIncapacityCertificateDocument: C80DocTypecodes_EvaluationOfMentalAndPhysicalIncapacityCertificateDocument,
  ReportingRateReportingPeriodPopulationCalculated: C80DocTypecodes_ReportingRateReportingPeriodPopulationCalculated,
  PerformanceRateReportingPeriodPopulationCalculated: C80DocTypecodes_PerformanceRateReportingPeriodPopulationCalculated,
  AxillaRightUS: C80DocTypecodes_AxillaRightUS,
  AxillaLeftUS: C80DocTypecodes_AxillaLeftUS,
  USGuidanceForInjectionOfJoint: C80DocTypecodes_USGuidanceForInjectionOfJoint,
  RectumAndColonCT3DWContrastIVAndWAirContrastPR: C80DocTypecodes_RectumAndColonCT3DWContrastIVAndWAirContrastPR,
  USGuidanceForAmbulatoryPhlebectomyOfExtremityVeinRight: C80DocTypecodes_USGuidanceForAmbulatoryPhlebectomyOfExtremityVeinRight,
  USGuidanceForAmbulatoryPhlebectomyOfExtremityVeinLeft: C80DocTypecodes_USGuidanceForAmbulatoryPhlebectomyOfExtremityVeinLeft,
  USGuidanceForLaserAblationOfVeinSOfExtremityVeinRight: C80DocTypecodes_USGuidanceForLaserAblationOfVeinSOfExtremityVeinRight,
  USGuidanceForLaserAblationOfVeinSOfExtremityVeinLeft: C80DocTypecodes_USGuidanceForLaserAblationOfVeinSOfExtremityVeinLeft,
  USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinsBilateral: C80DocTypecodes_USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinsBilateral,
  USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinBilateral: C80DocTypecodes_USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinBilateral,
  FluoroscopicAngiogramGuidanceForRemovalOfLongtermPeripheralCatheterFromCentralVein: C80DocTypecodes_FluoroscopicAngiogramGuidanceForRemovalOfLongtermPeripheralCatheterFromCentralVein,
  FluoroscopyGuidanceForPeripheralNerveDenervationOfUnspecifiedBodyRegion: C80DocTypecodes_FluoroscopyGuidanceForPeripheralNerveDenervationOfUnspecifiedBodyRegion,
  FluoroscopyGuidanceForFacetJointDenervationOfSpine: C80DocTypecodes_FluoroscopyGuidanceForFacetJointDenervationOfSpine,
  FluoroscopyGuidanceForFacetJointDenervationOfSpineCervical: C80DocTypecodes_FluoroscopyGuidanceForFacetJointDenervationOfSpineCervical,
  FluoroscopyGuidanceForFacetJointDenervationOfSpineLumbar: C80DocTypecodes_FluoroscopyGuidanceForFacetJointDenervationOfSpineLumbar,
  FluoroscopyGuidanceForIntercostalNerveDevervationOfSpineThoracic: C80DocTypecodes_FluoroscopyGuidanceForIntercostalNerveDevervationOfSpineThoracic,
  FluoroscopyGuidanceForRemovalOfPercutaneousNephrostomyTubeFromKidneyBilateralWContrast: C80DocTypecodes_FluoroscopyGuidanceForRemovalOfPercutaneousNephrostomyTubeFromKidneyBilateralWContrast,
  FluoroscopyGuidanceForReplacementOfPercutaneousDrainageTubeInBiliaryDuctsAndGallbladder: C80DocTypecodes_FluoroscopyGuidanceForReplacementOfPercutaneousDrainageTubeInBiliaryDuctsAndGallbladder,
  FluoroscopyGuidanceForRemovalOfCVALumenObstructionFromCentralVein: C80DocTypecodes_FluoroscopyGuidanceForRemovalOfCVALumenObstructionFromCentralVein,
  FluoroscopyGuidanceForRemovalOfCVADeviceObstructionFromCentralVein: C80DocTypecodes_FluoroscopyGuidanceForRemovalOfCVADeviceObstructionFromCentralVein,
  FluoroscopicAngiogramGuidanceForRemovalOfCatheterFromCentralVeinWContrastIV: C80DocTypecodes_FluoroscopicAngiogramGuidanceForRemovalOfCatheterFromCentralVeinWContrastIV,
  FluoroscopyGuidanceForRemovalOfCatheterFromCentralVeinTunneled: C80DocTypecodes_FluoroscopyGuidanceForRemovalOfCatheterFromCentralVeinTunneled,
  FluoroscopyGuidanceForRepairOfCVACatheterWithPortOrPumpOfCentralVein: C80DocTypecodes_FluoroscopyGuidanceForRepairOfCVACatheterWithPortOrPumpOfCentralVein,
  FluoroscopyGuidanceForRepairOfCVACatheterWithoutPortOrPumpOfCentralVein: C80DocTypecodes_FluoroscopyGuidanceForRepairOfCVACatheterWithoutPortOrPumpOfCentralVein,
  FluoroscopyGuidanceForKyphoplastyOfSpineLumbar: C80DocTypecodes_FluoroscopyGuidanceForKyphoplastyOfSpineLumbar,
  FluoroscopyGuidanceForKyphoplastyOfSpineThoracic: C80DocTypecodes_FluoroscopyGuidanceForKyphoplastyOfSpineThoracic,
  FluoroscopyGuidanceForTriggerPointInjectionOfMuscle: C80DocTypecodes_FluoroscopyGuidanceForTriggerPointInjectionOfMuscle,
  InterventionalRadiologyConsultNote: C80DocTypecodes_InterventionalRadiologyConsultNote,
  InterventionalRadiologyProgressNote: C80DocTypecodes_InterventionalRadiologyProgressNote,
  USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinsRight: C80DocTypecodes_USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinsRight,
  USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinsLeft: C80DocTypecodes_USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinsLeft,
  USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinRight: C80DocTypecodes_USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinRight,
  USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinLeft: C80DocTypecodes_USGuidanceForInjectionOfSclerosingAgentOfExtremityVeinLeft,
  ExtremityArteriesBilateralUSDopplerMultisectionAndPhysiologicArteryStudy: C80DocTypecodes_ExtremityArteriesBilateralUSDopplerMultisectionAndPhysiologicArteryStudy,
  ExtremityArteriesBilateralUSDopplerMultisectionLimitedAndPhysiologicArteryStudy: C80DocTypecodes_ExtremityArteriesBilateralUSDopplerMultisectionLimitedAndPhysiologicArteryStudy,
  ExtremityArteriesBilateralUSDopplerMultisectionAndPhysiologicArteryStudyAtRestAndWithExercise: C80DocTypecodes_ExtremityArteriesBilateralUSDopplerMultisectionAndPhysiologicArteryStudyAtRestAndWithExercise,
  SurgicalSpecimenXRay: C80DocTypecodes_SurgicalSpecimenXRay,
  CommunicationOfCriticalResultsDescriptionDocument: C80DocTypecodes_CommunicationOfCriticalResultsDescriptionDocument,
  RadiationExposureAndProtectionInformationDescriptionDocumentDiagnosticImaging: C80DocTypecodes_RadiationExposureAndProtectionInformationDescriptionDocumentDiagnosticImaging,
  RadiologyConsultNote: C80DocTypecodes_RadiologyConsultNote,
  PrescriptionRequestPharmacyDocumentFromPharmacist: C80DocTypecodes_PrescriptionRequestPharmacyDocumentFromPharmacist,
  FDAPackageInsertIndexingProductConcept: C80DocTypecodes_FDAPackageInsertIndexingProductConcept,
  ReportSectionHeadingUnspecifiedBodyRegion: C80DocTypecodes_ReportSectionHeadingUnspecifiedBodyRegion,
  PeriodontalServiceAttachment: C80DocTypecodes_PeriodontalServiceAttachment,
  MeasureDescriptionNarrative: C80DocTypecodes_MeasureDescriptionNarrative,
  ComplexMedicalConditionsActionPlan: C80DocTypecodes_ComplexMedicalConditionsActionPlan,
  MultipleSclerosisActionPlan: C80DocTypecodes_MultipleSclerosisActionPlan,
  HeartDiseaseActionPlan: C80DocTypecodes_HeartDiseaseActionPlan,
  MuscularDystrophyActionPlan: C80DocTypecodes_MuscularDystrophyActionPlan,
  CysticFibrosisActionPlan: C80DocTypecodes_CysticFibrosisActionPlan,
  InflammatoryBowelDiseaseActionPlan: C80DocTypecodes_InflammatoryBowelDiseaseActionPlan,
  DiabetesTypeIActionPlan: C80DocTypecodes_DiabetesTypeIActionPlan,
  DiabetesTypeIIActionPlan: C80DocTypecodes_DiabetesTypeIIActionPlan,
  AnaphylaxisActionPlan: C80DocTypecodes_AnaphylaxisActionPlan,
  SeizureDisorderActionPlan: C80DocTypecodes_SeizureDisorderActionPlan,
  AutismActionPlan: C80DocTypecodes_AutismActionPlan,
  ADHDActionPlan: C80DocTypecodes_ADHDActionPlan,
  OncologyTreatmentPlanAndSummaryDocument: C80DocTypecodes_OncologyTreatmentPlanAndSummaryDocument,
  OccupationalSummaryNote: C80DocTypecodes_OccupationalSummaryNote,
  InterRAIEmergencyScreenerForPsychiatryESPDocument: C80DocTypecodes_InterRAIEmergencyScreenerForPsychiatryESPDocument,
  InterRAIAcuteCareACHospitalDocument: C80DocTypecodes_InterRAIAcuteCareACHospitalDocument,
  InterRAIPalliativeCarePCDocument: C80DocTypecodes_InterRAIPalliativeCarePCDocument,
  InterRAICommunityHealthAssessmentDeafblindSupplementCHADbDocument: C80DocTypecodes_InterRAICommunityHealthAssessmentDeafblindSupplementCHADbDocument,
  InterRAICommunityHealthAssessmentAssistedLivingSupplementCHAALDocument: C80DocTypecodes_InterRAICommunityHealthAssessmentAssistedLivingSupplementCHAALDocument,
  InterRAICommunityHealthAssessmentMentalHealthSupplementCHAMHDocument: C80DocTypecodes_InterRAICommunityHealthAssessmentMentalHealthSupplementCHAMHDocument,
  InterRAICommunityHealthAssessmentFunctionalSupplementCHAFSDocument: C80DocTypecodes_InterRAICommunityHealthAssessmentFunctionalSupplementCHAFSDocument,
  InterRAICommunityHealthAssessmentCHADocument: C80DocTypecodes_InterRAICommunityHealthAssessmentCHADocument,
  InterRAILongTermCareFacilityLTCFDocument: C80DocTypecodes_InterRAILongTermCareFacilityLTCFDocument,
  InterRAIHomeCareHCDocument: C80DocTypecodes_InterRAIHomeCareHCDocument,
  InterRAIContactAssessmentCADocument: C80DocTypecodes_InterRAIContactAssessmentCADocument,
  TraumaSummaryRegistryReportDocument: C80DocTypecodes_TraumaSummaryRegistryReportDocument,
  PrehospitalSummaryDocument: C80DocTypecodes_PrehospitalSummaryDocument,
  DemographicInformationPlusHistoryOfOccupationDocument: C80DocTypecodes_DemographicInformationPlusHistoryOfOccupationDocument,
  InjuryEventSummaryDocument: C80DocTypecodes_InjuryEventSummaryDocument,
  SummaryOfEpisodeNoteEmergencyDepartmentPlusHospital: C80DocTypecodes_SummaryOfEpisodeNoteEmergencyDepartmentPlusHospital,
  DischargeInstructions: C80DocTypecodes_DischargeInstructions,
  HIVSummaryRegistryReportDocument: C80DocTypecodes_HIVSummaryRegistryReportDocument,
  IndividualCounselingNote: C80DocTypecodes_IndividualCounselingNote,
  QuestionnaireResponseSectionDocument: C80DocTypecodes_QuestionnaireResponseSectionDocument,
  QuestionnaireFormDefinitionSectionDocument: C80DocTypecodes_QuestionnaireFormDefinitionSectionDocument,
  ClinicalDocumentSettingFromLOINCDocumentOntology: C80DocTypecodes_ClinicalDocumentSettingFromLOINCDocumentOntology,
  ClinicalDocumentKindOfDocumentFromLOINCDocumentOntology: C80DocTypecodes_ClinicalDocumentKindOfDocumentFromLOINCDocumentOntology,
  ClinicalDocumentTypeOfServiceFromLOINCDocumentOntology: C80DocTypecodes_ClinicalDocumentTypeOfServiceFromLOINCDocumentOntology,
  ClinicalDocumentRoleFromLOINCDocumentOntology: C80DocTypecodes_ClinicalDocumentRoleFromLOINCDocumentOntology,
  ClinicalDocumentSubjectMatterDomainFromLOINCDocumentOntology: C80DocTypecodes_ClinicalDocumentSubjectMatterDomainFromLOINCDocumentOntology,
  HospitalDischargeInstructions: C80DocTypecodes_HospitalDischargeInstructions,
};
const C80Facilitycodes_HospitalOutpatientDentalClinic: Coding = {
  code: '10206005',
  display: 'Hospital outpatient dental clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_FreeStandingAmbulatorySurgeryFacility: Coding = {
  code: '10531005',
  display: 'Free-standing ambulatory surgery facility',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_AmbulanceBasedCare: Coding = {
  code: '11424001',
  display: 'Ambulance-based care',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientMentalHealthCenter: Coding = {
  code: '14866005',
  display: 'Hospital outpatient mental health center',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_FreeStandingRadiologyFacility: Coding = {
  code: '1773006',
  display: 'Free-standing radiology facility',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientGeriatricHealthCenter: Coding = {
  code: '1814000',
  display: 'Hospital outpatient geriatric health center',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_FeeForServicePrivatePhysiciansQuoteGroupOffice: Coding = {
  code: '19602009',
  display: "Fee-for-service private physicians' group office",
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_SubstanceAbuseTreatmentCenter: Coding = {
  code: '20078004',
  display: 'Substance abuse treatment center',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalShip: Coding = {
  code: '2081004',
  display: 'Hospital ship',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalPrison: Coding = {
  code: '224687002',
  display: 'Hospital-prison',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientGynecologyClinic: Coding = {
  code: '22549003',
  display: 'Hospital outpatient gynecology clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalCommunity: Coding = {
  code: '225732001',
  display: 'Hospital-community',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientOtorhinolaryngologyClinic: Coding = {
  code: '23392004',
  display: 'Hospital outpatient otorhinolaryngology clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_SexuallyTransmittedDiseaseHealthCenter: Coding = {
  code: '25681007',
  display: 'Sexually transmitted disease health center',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_SportsFacility: Coding = {
  code: '272501009',
  display: 'Sports facility',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_ElderlyAssessmentClinic: Coding = {
  code: '275576008',
  display: 'Elderly assessment clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospiceFacility: Coding = {
  code: '284546000',
  display: 'Hospice facility',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientInfectiousDiseaseClinic: Coding = {
  code: '2849009',
  display: 'Hospital outpatient infectious disease clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_PsychogeriatricDayHospital: Coding = {
  code: '309898008',
  display: 'Psychogeriatric day hospital',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_CareOfTheElderlyDayHospital: Coding = {
  code: '309900005',
  display: 'Care of the elderly day hospital',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_PrivateResidentialHome: Coding = {
  code: '310205006',
  display: 'Private residential home',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientFamilyMedicineClinic: Coding = {
  code: '31628002',
  display: 'Hospital outpatient family medicine clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalLongTermCare: Coding = {
  code: '32074000',
  display: 'Hospital-long term care',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalBasedOutpatientClinicOrDepartmentOTHERNOTLISTED: Coding = {
  code: '33022008',
  display: 'Hospital-based outpatient clinic or department--OTHER-NOT LISTED',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientRheumatologyClinic: Coding = {
  code: '331006',
  display: 'Hospital outpatient rheumatology clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_AmbulatoryCareSiteOTHERNOTLISTED: Coding = {
  code: '35971002',
  display: 'Ambulatory care site--OTHER--NOT LISTED',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientAllergyClinic: Coding = {
  code: '360957003',
  display: 'Hospital outpatient allergy clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientImmunologyClinic: Coding = {
  code: '360966004',
  display: 'Hospital outpatient immunology clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalTraumaCenter: Coding = {
  code: '36125001',
  display: 'Hospital-trauma center',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientPainClinic: Coding = {
  code: '36293008',
  display: 'Hospital outpatient pain clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientPediatricClinic: Coding = {
  code: '3729002',
  display: 'Hospital outpatient pediatric clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientRehabilitationClinic: Coding = {
  code: '37546005',
  display: 'Hospital outpatient rehabilitation clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientDermatologyClinic: Coding = {
  code: '37550003',
  display: 'Hospital outpatient dermatology clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientNeurologyClinic: Coding = {
  code: '38238005',
  display: 'Hospital outpatient neurology clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_PrivatePhysiciansQuoteGroupOffice: Coding = {
  code: '39350007',
  display: "Private physicians' group office",
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_IndependentAmbulatoryCareProviderSiteOTHERNOTLISTED: Coding = {
  code: '394759007',
  display: 'Independent ambulatory care provider site--OTHER--NOT LISTED',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HealthEncounterSiteNOTLISTED: Coding = {
  code: '394777002',
  display: 'Health encounter site--NOT LISTED',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_ResidentialSchoolInfirmary: Coding = {
  code: '39913001',
  display: 'Residential school infirmary',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_AmbulatorySurgeryCenter: Coding = {
  code: '405607001',
  display: 'Ambulatory surgery center',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_ContainedCasualtySetting: Coding = {
  code: '409519008',
  display: 'Contained casualty setting',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_AdultDayCareCenter: Coding = {
  code: '413456002',
  display: 'Adult day care center',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_ChildDayCareCenter: Coding = {
  code: '413817003',
  display: 'Child day care center',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_FreeStandingGeriatricHealthCenter: Coding = {
  code: '41844007',
  display: 'Free-standing geriatric health center',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_DialysisUnitHospital: Coding = {
  code: '418518002',
  display: 'Dialysis unit--hospital',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_ResidentialInstitution: Coding = {
  code: '419955002',
  display: 'Residential institution',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_NursingHome: Coding = {
  code: '42665001',
  display: 'Nursing home',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalMilitaryField: Coding = {
  code: '4322002',
  display: 'Hospital-military field',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_SkilledNursingFacility: Coding = {
  code: '45618002',
  display: 'Skilled nursing facility',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_FreeStandingLaboratoryFacility: Coding = {
  code: '45899008',
  display: 'Free-standing laboratory facility',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_VaccinationClinic: Coding = {
  code: '46224007',
  display: 'Vaccination clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalVeteransQuoteAdministration: Coding = {
  code: '48311003',
  display: "Hospital-Veterans' Administration",
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientUrologyClinic: Coding = {
  code: '50569004',
  display: 'Hospital outpatient urology clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_FreeStandingMentalHealthCenter: Coding = {
  code: '51563005',
  display: 'Free-standing mental health center',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalBirthingCenter: Coding = {
  code: '52668009',
  display: 'Hospital birthing center',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientPeripheralVascularClinic: Coding = {
  code: '5584006',
  display: 'Hospital outpatient peripheral vascular clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientObstetricalClinic: Coding = {
  code: '56189001',
  display: 'Hospital outpatient obstetrical clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientHematologyClinic: Coding = {
  code: '56293002',
  display: 'Hospital outpatient hematology clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientRespiratoryDiseaseClinic: Coding = {
  code: '57159002',
  display: 'Hospital outpatient respiratory disease clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientGastroenterologyClinic: Coding = {
  code: '58482006',
  display: 'Hospital outpatient gastroenterology clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_TravelerQuoteSAidClinic: Coding = {
  code: '59374000',
  display: "Traveler's aid clinic",
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalPsychiatric: Coding = {
  code: '62480006',
  display: 'Hospital-psychiatric',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_LocalCommunityHealthCenter: Coding = {
  code: '6827000',
  display: 'Local community health center',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalAmbulatorySurgeryFacility: Coding = {
  code: '69362002',
  display: 'Hospital ambulatory surgery facility',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HealthMaintenanceOrganization: Coding = {
  code: '72311000',
  display: 'Health maintenance organization',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientEndocrinologyClinic: Coding = {
  code: '73644007',
  display: 'Hospital outpatient endocrinology clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_EmergencyDepartmentHospital: Coding = {
  code: '73770003',
  display: 'Emergency department--hospital',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_RuralHealthCenter: Coding = {
  code: '77931003',
  display: 'Rural health center',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientOrthopedicsClinic: Coding = {
  code: '78001009',
  display: 'Hospital outpatient orthopedics clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientOphthalmologyClinic: Coding = {
  code: '78088001',
  display: 'Hospital outpatient ophthalmology clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalRadiologyFacility: Coding = {
  code: '79491001',
  display: 'Hospital radiology facility',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalGovernment: Coding = {
  code: '79993009',
  display: 'Hospital-government',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalRehabilitation: Coding = {
  code: '80522000',
  display: 'Hospital-rehabilitation',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_WalkInClinic: Coding = {
  code: '81234003',
  display: 'Walk-in clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalChildrenQuoteS: Coding = {
  code: '82242000',
  display: "Hospital-children's",
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_SoloPracticePrivateOffice: Coding = {
  code: '83891005',
  display: 'Solo practice private office',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientOncologyClinic: Coding = {
  code: '89972002',
  display: 'Hospital outpatient oncology clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HelicopterBasedCare: Coding = {
  code: '901005',
  display: 'Helicopter-based care',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_HospitalOutpatientGeneralSurgeryClinic: Coding = {
  code: '90484001',
  display: 'Hospital outpatient general surgery clinic',
  system: 'http://snomed.info/sct',
};
const C80Facilitycodes_FreeStandingBirthingCenter: Coding = {
  code: '91154008',
  display: 'Free-standing birthing center',
  system: 'http://snomed.info/sct',
};
/**
 * This is the code representing the type of organizational setting where the clinical encounter, service, interaction, or treatment occurred. The value set used for Healthcare Facility Type has been defined by HITSP to be the value set reproduced from HITSP C80 Table 2-147.
 */
export const C80Facilitycodes = {
  HospitalOutpatientDentalClinic: C80Facilitycodes_HospitalOutpatientDentalClinic,
  FreeStandingAmbulatorySurgeryFacility: C80Facilitycodes_FreeStandingAmbulatorySurgeryFacility,
  AmbulanceBasedCare: C80Facilitycodes_AmbulanceBasedCare,
  HospitalOutpatientMentalHealthCenter: C80Facilitycodes_HospitalOutpatientMentalHealthCenter,
  FreeStandingRadiologyFacility: C80Facilitycodes_FreeStandingRadiologyFacility,
  HospitalOutpatientGeriatricHealthCenter: C80Facilitycodes_HospitalOutpatientGeriatricHealthCenter,
  FeeForServicePrivatePhysiciansQuoteGroupOffice: C80Facilitycodes_FeeForServicePrivatePhysiciansQuoteGroupOffice,
  SubstanceAbuseTreatmentCenter: C80Facilitycodes_SubstanceAbuseTreatmentCenter,
  HospitalShip: C80Facilitycodes_HospitalShip,
  HospitalPrison: C80Facilitycodes_HospitalPrison,
  HospitalOutpatientGynecologyClinic: C80Facilitycodes_HospitalOutpatientGynecologyClinic,
  HospitalCommunity: C80Facilitycodes_HospitalCommunity,
  HospitalOutpatientOtorhinolaryngologyClinic: C80Facilitycodes_HospitalOutpatientOtorhinolaryngologyClinic,
  SexuallyTransmittedDiseaseHealthCenter: C80Facilitycodes_SexuallyTransmittedDiseaseHealthCenter,
  SportsFacility: C80Facilitycodes_SportsFacility,
  ElderlyAssessmentClinic: C80Facilitycodes_ElderlyAssessmentClinic,
  HospiceFacility: C80Facilitycodes_HospiceFacility,
  HospitalOutpatientInfectiousDiseaseClinic: C80Facilitycodes_HospitalOutpatientInfectiousDiseaseClinic,
  PsychogeriatricDayHospital: C80Facilitycodes_PsychogeriatricDayHospital,
  CareOfTheElderlyDayHospital: C80Facilitycodes_CareOfTheElderlyDayHospital,
  PrivateResidentialHome: C80Facilitycodes_PrivateResidentialHome,
  HospitalOutpatientFamilyMedicineClinic: C80Facilitycodes_HospitalOutpatientFamilyMedicineClinic,
  HospitalLongTermCare: C80Facilitycodes_HospitalLongTermCare,
  HospitalBasedOutpatientClinicOrDepartmentOTHERNOTLISTED: C80Facilitycodes_HospitalBasedOutpatientClinicOrDepartmentOTHERNOTLISTED,
  HospitalOutpatientRheumatologyClinic: C80Facilitycodes_HospitalOutpatientRheumatologyClinic,
  AmbulatoryCareSiteOTHERNOTLISTED: C80Facilitycodes_AmbulatoryCareSiteOTHERNOTLISTED,
  HospitalOutpatientAllergyClinic: C80Facilitycodes_HospitalOutpatientAllergyClinic,
  HospitalOutpatientImmunologyClinic: C80Facilitycodes_HospitalOutpatientImmunologyClinic,
  HospitalTraumaCenter: C80Facilitycodes_HospitalTraumaCenter,
  HospitalOutpatientPainClinic: C80Facilitycodes_HospitalOutpatientPainClinic,
  HospitalOutpatientPediatricClinic: C80Facilitycodes_HospitalOutpatientPediatricClinic,
  HospitalOutpatientRehabilitationClinic: C80Facilitycodes_HospitalOutpatientRehabilitationClinic,
  HospitalOutpatientDermatologyClinic: C80Facilitycodes_HospitalOutpatientDermatologyClinic,
  HospitalOutpatientNeurologyClinic: C80Facilitycodes_HospitalOutpatientNeurologyClinic,
  PrivatePhysiciansQuoteGroupOffice: C80Facilitycodes_PrivatePhysiciansQuoteGroupOffice,
  IndependentAmbulatoryCareProviderSiteOTHERNOTLISTED: C80Facilitycodes_IndependentAmbulatoryCareProviderSiteOTHERNOTLISTED,
  HealthEncounterSiteNOTLISTED: C80Facilitycodes_HealthEncounterSiteNOTLISTED,
  ResidentialSchoolInfirmary: C80Facilitycodes_ResidentialSchoolInfirmary,
  AmbulatorySurgeryCenter: C80Facilitycodes_AmbulatorySurgeryCenter,
  ContainedCasualtySetting: C80Facilitycodes_ContainedCasualtySetting,
  AdultDayCareCenter: C80Facilitycodes_AdultDayCareCenter,
  ChildDayCareCenter: C80Facilitycodes_ChildDayCareCenter,
  FreeStandingGeriatricHealthCenter: C80Facilitycodes_FreeStandingGeriatricHealthCenter,
  DialysisUnitHospital: C80Facilitycodes_DialysisUnitHospital,
  ResidentialInstitution: C80Facilitycodes_ResidentialInstitution,
  NursingHome: C80Facilitycodes_NursingHome,
  HospitalMilitaryField: C80Facilitycodes_HospitalMilitaryField,
  SkilledNursingFacility: C80Facilitycodes_SkilledNursingFacility,
  FreeStandingLaboratoryFacility: C80Facilitycodes_FreeStandingLaboratoryFacility,
  VaccinationClinic: C80Facilitycodes_VaccinationClinic,
  HospitalVeteransQuoteAdministration: C80Facilitycodes_HospitalVeteransQuoteAdministration,
  HospitalOutpatientUrologyClinic: C80Facilitycodes_HospitalOutpatientUrologyClinic,
  FreeStandingMentalHealthCenter: C80Facilitycodes_FreeStandingMentalHealthCenter,
  HospitalBirthingCenter: C80Facilitycodes_HospitalBirthingCenter,
  HospitalOutpatientPeripheralVascularClinic: C80Facilitycodes_HospitalOutpatientPeripheralVascularClinic,
  HospitalOutpatientObstetricalClinic: C80Facilitycodes_HospitalOutpatientObstetricalClinic,
  HospitalOutpatientHematologyClinic: C80Facilitycodes_HospitalOutpatientHematologyClinic,
  HospitalOutpatientRespiratoryDiseaseClinic: C80Facilitycodes_HospitalOutpatientRespiratoryDiseaseClinic,
  HospitalOutpatientGastroenterologyClinic: C80Facilitycodes_HospitalOutpatientGastroenterologyClinic,
  TravelerQuoteSAidClinic: C80Facilitycodes_TravelerQuoteSAidClinic,
  HospitalPsychiatric: C80Facilitycodes_HospitalPsychiatric,
  LocalCommunityHealthCenter: C80Facilitycodes_LocalCommunityHealthCenter,
  HospitalAmbulatorySurgeryFacility: C80Facilitycodes_HospitalAmbulatorySurgeryFacility,
  HealthMaintenanceOrganization: C80Facilitycodes_HealthMaintenanceOrganization,
  HospitalOutpatientEndocrinologyClinic: C80Facilitycodes_HospitalOutpatientEndocrinologyClinic,
  EmergencyDepartmentHospital: C80Facilitycodes_EmergencyDepartmentHospital,
  RuralHealthCenter: C80Facilitycodes_RuralHealthCenter,
  HospitalOutpatientOrthopedicsClinic: C80Facilitycodes_HospitalOutpatientOrthopedicsClinic,
  HospitalOutpatientOphthalmologyClinic: C80Facilitycodes_HospitalOutpatientOphthalmologyClinic,
  HospitalRadiologyFacility: C80Facilitycodes_HospitalRadiologyFacility,
  HospitalGovernment: C80Facilitycodes_HospitalGovernment,
  HospitalRehabilitation: C80Facilitycodes_HospitalRehabilitation,
  WalkInClinic: C80Facilitycodes_WalkInClinic,
  HospitalChildrenQuoteS: C80Facilitycodes_HospitalChildrenQuoteS,
  SoloPracticePrivateOffice: C80Facilitycodes_SoloPracticePrivateOffice,
  HospitalOutpatientOncologyClinic: C80Facilitycodes_HospitalOutpatientOncologyClinic,
  HelicopterBasedCare: C80Facilitycodes_HelicopterBasedCare,
  HospitalOutpatientGeneralSurgeryClinic: C80Facilitycodes_HospitalOutpatientGeneralSurgeryClinic,
  FreeStandingBirthingCenter: C80Facilitycodes_FreeStandingBirthingCenter,
};
const C80PracticeCodes_PediatricSurgery: Coding = {
  code: '394539006',
  display: 'Pediatric surgery',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgicalAccidentAndEmergency: Coding = {
  code: '394576009',
  display: 'Surgical-Accident & emergency',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Anesthetics: Coding = {
  code: '394577000',
  display: 'Anesthetics',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_AudiologicalMedicine: Coding = {
  code: '394578005',
  display: 'Audiological medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Cardiology: Coding = {
  code: '394579002',
  display: 'Cardiology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_ClinicalGenetics: Coding = {
  code: '394580004',
  display: 'Clinical genetics',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_CommunityMedicine: Coding = {
  code: '394581000',
  display: 'Community medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Dermatology: Coding = {
  code: '394582007',
  display: 'Dermatology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Endocrinology: Coding = {
  code: '394583002',
  display: 'Endocrinology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Gastroenterology: Coding = {
  code: '394584008',
  display: 'Gastroenterology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_ObstetricsAndGynecology: Coding = {
  code: '394585009',
  display: 'Obstetrics and gynecology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Gynecology: Coding = {
  code: '394586005',
  display: 'Gynecology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Psychiatry: Coding = {
  code: '394587001',
  display: 'Psychiatry',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricChildAndAdolescentPsychiatry: Coding = {
  code: '394588006',
  display: 'Pediatric (Child and adolescent) psychiatry',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Nephrology: Coding = {
  code: '394589003',
  display: 'Nephrology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_ThoracicMedicine: Coding = {
  code: '394590007',
  display: 'Thoracic medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Neurology: Coding = {
  code: '394591006',
  display: 'Neurology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_ClinicalOncology: Coding = {
  code: '394592004',
  display: 'Clinical oncology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_MedicalOncology: Coding = {
  code: '394593009',
  display: 'Medical oncology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Ophthalmology: Coding = {
  code: '394594003',
  display: 'Ophthalmology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Histopathology: Coding = {
  code: '394597005',
  display: 'Histopathology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Immunopathology: Coding = {
  code: '394598000',
  display: 'Immunopathology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Neuropathology: Coding = {
  code: '394599008',
  display: 'Neuropathology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_ClinicalPharmacology: Coding = {
  code: '394600006',
  display: 'Clinical pharmacology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_ClinicalPhysiology: Coding = {
  code: '394601005',
  display: 'Clinical physiology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Rehabilitation: Coding = {
  code: '394602003',
  display: 'Rehabilitation',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryEarNoseAndThroatSurgery: Coding = {
  code: '394604002',
  display: 'Surgery-Ear, nose and throat surgery',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryDentalOralSurgery: Coding = {
  code: '394605001',
  display: 'Surgery-Dental-Oral surgery',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryDentistryRestorativeDentistry: Coding = {
  code: '394606000',
  display: 'Surgery-Dentistry-Restorative dentistry',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricDentistry: Coding = {
  code: '394607009',
  display: 'Pediatric dentistry',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryDentalOrthodontics: Coding = {
  code: '394608004',
  display: 'Surgery-Dental-Orthodontics',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryGeneral: Coding = {
  code: '394609007',
  display: 'Surgery-general',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryNeurosurgery: Coding = {
  code: '394610002',
  display: 'Surgery-Neurosurgery',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryPlasticSurgery: Coding = {
  code: '394611003',
  display: 'Surgery-Plastic surgery',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Urology: Coding = {
  code: '394612005',
  display: 'Urology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_NuclearMedicine: Coding = {
  code: '394649004',
  display: 'Nuclear medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgicalSpecialtyOTHERNOTLISTED: Coding = {
  code: '394732004',
  display: 'Surgical specialty--OTHER-NOT LISTED',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_MedicalSpecialtyOTHERNOTLISTED: Coding = {
  code: '394733009',
  display: 'Medical specialty--OTHER--NOT LISTED',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryTraumaAndOrthopedics: Coding = {
  code: '394801008',
  display: 'Surgery-Trauma and orthopedics',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_GeneralMedicine: Coding = {
  code: '394802001',
  display: 'General medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_ClinicalHematology: Coding = {
  code: '394803006',
  display: 'Clinical hematology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_ClinicalCytogeneticsAndMolecularGenetics: Coding = {
  code: '394804000',
  display: 'Clinical cytogenetics and molecular genetics',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PalliativeMedicine: Coding = {
  code: '394806003',
  display: 'Palliative medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_InfectiousDiseases: Coding = {
  code: '394807007',
  display: 'Infectious diseases',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_GenitoUrinaryMedicine: Coding = {
  code: '394808002',
  display: 'Genito-urinary medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_ClinicalNeuroPhysiology: Coding = {
  code: '394809005',
  display: 'Clinical neuro-physiology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Rheumatology: Coding = {
  code: '394810000',
  display: 'Rheumatology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_GeriatricMedicine: Coding = {
  code: '394811001',
  display: 'Geriatric medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_DentalMedicineSpecialties: Coding = {
  code: '394812008',
  display: 'Dental medicine specialties',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_MedicalOphthalmology: Coding = {
  code: '394813003',
  display: 'Medical ophthalmology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_GeneralPractice: Coding = {
  code: '394814009',
  display: 'General practice',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_OccupationalMedicine: Coding = {
  code: '394821009',
  display: 'Occupational medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PainManagement: Coding = {
  code: '394882004',
  display: 'Pain management',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Psychotherapy: Coding = {
  code: '394913002',
  display: 'Psychotherapy',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Radiology: Coding = {
  code: '394914008',
  display: 'Radiology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_GeneralPathology: Coding = {
  code: '394915009',
  display: 'General pathology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Hematopathology: Coding = {
  code: '394916005',
  display: 'Hematopathology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PublicHealthMedicine: Coding = {
  code: '408440000',
  display: 'Public health medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryDentalEndodontics: Coding = {
  code: '408441001',
  display: 'Surgery-Dental-Endodontics',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_GeneralMedicalPractice: Coding = {
  code: '408443003',
  display: 'General medical practice',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_DentalGeneralDentalPractice: Coding = {
  code: '408444009',
  display: 'Dental-General dental practice',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_GynecologicalOncology: Coding = {
  code: '408446006',
  display: 'Gynecological oncology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_RespiteCare: Coding = {
  code: '408447002',
  display: 'Respite care',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_TropicalMedicine: Coding = {
  code: '408448007',
  display: 'Tropical medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryDentistrySurgical: Coding = {
  code: '408449004',
  display: 'Surgery-Dentistry--surgical',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SleepStudies: Coding = {
  code: '408450004',
  display: 'Sleep studies',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_ClinicalMicrobiology: Coding = {
  code: '408454008',
  display: 'Clinical microbiology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_RadiologyInterventionalRadiology: Coding = {
  code: '408455009',
  display: 'Radiology-Interventional radiology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricCardiology: Coding = {
  code: '408459003',
  display: 'Pediatric cardiology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryDentalProstheticDentistryProsthodontics: Coding = {
  code: '408460008',
  display: 'Surgery-Dental-Prosthetic dentistry (Prosthodontics)',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryDentalPeriodontalSurgery: Coding = {
  code: '408461007',
  display: 'Surgery-Dental-Periodontal surgery',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_BurnsCare: Coding = {
  code: '408462000',
  display: 'Burns care',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryVascular: Coding = {
  code: '408463005',
  display: 'Surgery-Vascular',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryColorectalSurgery: Coding = {
  code: '408464004',
  display: 'Surgery-Colorectal surgery',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryDentalOralAndMaxillofacialSurgery: Coding = {
  code: '408465003',
  display: 'Surgery-Dental-Oral and maxillofacial surgery',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryCardiacSurgery: Coding = {
  code: '408466002',
  display: 'Surgery-Cardiac surgery',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_AdultMentalIllness: Coding = {
  code: '408467006',
  display: 'Adult mental illness',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_LearningDisability: Coding = {
  code: '408468001',
  display: 'Learning disability',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryBreastSurgery: Coding = {
  code: '408469009',
  display: 'Surgery-Breast surgery',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Obstetrics: Coding = {
  code: '408470005',
  display: 'Obstetrics',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryCardiothoracicTransplantation: Coding = {
  code: '408471009',
  display: 'Surgery-Cardiothoracic transplantation',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Hepatology: Coding = {
  code: '408472002',
  display: 'Hepatology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryHepatobiliaryAndPancreaticSurgery: Coding = {
  code: '408474001',
  display: 'Surgery-Hepatobiliary and pancreatic surgery',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_DiabeticMedicine: Coding = {
  code: '408475000',
  display: 'Diabetic medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryBoneAndMarrowTransplantation: Coding = {
  code: '408476004',
  display: 'Surgery-Bone and marrow transplantation',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryTransplantationSurgery: Coding = {
  code: '408477008',
  display: 'Surgery-Transplantation surgery',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_CriticalCareMedicine: Coding = {
  code: '408478003',
  display: 'Critical care medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_ClinicalImmunology: Coding = {
  code: '408480009',
  display: 'Clinical immunology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Toxicology: Coding = {
  code: '409967009',
  display: 'Toxicology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PreventiveMedicine: Coding = {
  code: '409968004',
  display: 'Preventive medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_MilitaryMedicine: Coding = {
  code: '410001006',
  display: 'Military medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_DiveMedicine: Coding = {
  code: '410005002',
  display: 'Dive medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_OsteopathicManipulativeMedicine: Coding = {
  code: '416304004',
  display: 'Osteopathic manipulative medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricOncology: Coding = {
  code: '418002000',
  display: 'Pediatric oncology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgeryDermatologicSurgery: Coding = {
  code: '418018006',
  display: 'Surgery-Dermatologic surgery',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricGastroenterology: Coding = {
  code: '418058008',
  display: 'Pediatric gastroenterology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PulmonaryMedicine: Coding = {
  code: '418112009',
  display: 'Pulmonary medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricImmunology: Coding = {
  code: '418535003',
  display: 'Pediatric immunology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricHematology: Coding = {
  code: '418652005',
  display: 'Pediatric hematology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricInfectiousDiseases: Coding = {
  code: '418862001',
  display: 'Pediatric infectious diseases',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_Otolaryngology: Coding = {
  code: '418960008',
  display: 'Otolaryngology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_UrologicalOncology: Coding = {
  code: '419043006',
  display: 'Urological oncology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricPulmonology: Coding = {
  code: '419170002',
  display: 'Pediatric pulmonology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_InternalMedicine: Coding = {
  code: '419192003',
  display: 'Internal medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_SurgicalOncology: Coding = {
  code: '419321007',
  display: 'Surgical oncology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricNephrology: Coding = {
  code: '419365004',
  display: 'Pediatric nephrology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricRheumatology: Coding = {
  code: '419472004',
  display: 'Pediatric rheumatology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricEndocrinology: Coding = {
  code: '419610006',
  display: 'Pediatric endocrinology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_FamilyPractice: Coding = {
  code: '419772000',
  display: 'Family practice',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_RadiationOncology: Coding = {
  code: '419815003',
  display: 'Radiation oncology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricOphthalmology: Coding = {
  code: '419983000',
  display: 'Pediatric ophthalmology',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricSurgeryBoneMarrowTransplantation: Coding = {
  code: '420112009',
  display: 'Pediatric surgery-bone marrow transplantation',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_PediatricGenetics: Coding = {
  code: '420208008',
  display: 'Pediatric genetics',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_BloodBankingAndTransfusionMedicine: Coding = {
  code: '421661004',
  display: 'Blood banking and transfusion medicine',
  system: 'http://snomed.info/sct',
};
const C80PracticeCodes_OphthalmicSurgery: Coding = {
  code: '422191005',
  display: 'Ophthalmic surgery',
  system: 'http://snomed.info/sct',
};
/**
 * This is the code representing the clinical specialty of the clinician or provider who interacted with, treated, or provided a service to/for the patient. The value set used for clinical specialty has been limited by HITSP to the value set reproduced from HITSP C80 Table 2-149 Clinical Specialty Value Set Definition.
 */
export const C80PracticeCodes = {
  PediatricSurgery: C80PracticeCodes_PediatricSurgery,
  SurgicalAccidentAndEmergency: C80PracticeCodes_SurgicalAccidentAndEmergency,
  Anesthetics: C80PracticeCodes_Anesthetics,
  AudiologicalMedicine: C80PracticeCodes_AudiologicalMedicine,
  Cardiology: C80PracticeCodes_Cardiology,
  ClinicalGenetics: C80PracticeCodes_ClinicalGenetics,
  CommunityMedicine: C80PracticeCodes_CommunityMedicine,
  Dermatology: C80PracticeCodes_Dermatology,
  Endocrinology: C80PracticeCodes_Endocrinology,
  Gastroenterology: C80PracticeCodes_Gastroenterology,
  ObstetricsAndGynecology: C80PracticeCodes_ObstetricsAndGynecology,
  Gynecology: C80PracticeCodes_Gynecology,
  Psychiatry: C80PracticeCodes_Psychiatry,
  PediatricChildAndAdolescentPsychiatry: C80PracticeCodes_PediatricChildAndAdolescentPsychiatry,
  Nephrology: C80PracticeCodes_Nephrology,
  ThoracicMedicine: C80PracticeCodes_ThoracicMedicine,
  Neurology: C80PracticeCodes_Neurology,
  ClinicalOncology: C80PracticeCodes_ClinicalOncology,
  MedicalOncology: C80PracticeCodes_MedicalOncology,
  Ophthalmology: C80PracticeCodes_Ophthalmology,
  Histopathology: C80PracticeCodes_Histopathology,
  Immunopathology: C80PracticeCodes_Immunopathology,
  Neuropathology: C80PracticeCodes_Neuropathology,
  ClinicalPharmacology: C80PracticeCodes_ClinicalPharmacology,
  ClinicalPhysiology: C80PracticeCodes_ClinicalPhysiology,
  Rehabilitation: C80PracticeCodes_Rehabilitation,
  SurgeryEarNoseAndThroatSurgery: C80PracticeCodes_SurgeryEarNoseAndThroatSurgery,
  SurgeryDentalOralSurgery: C80PracticeCodes_SurgeryDentalOralSurgery,
  SurgeryDentistryRestorativeDentistry: C80PracticeCodes_SurgeryDentistryRestorativeDentistry,
  PediatricDentistry: C80PracticeCodes_PediatricDentistry,
  SurgeryDentalOrthodontics: C80PracticeCodes_SurgeryDentalOrthodontics,
  SurgeryGeneral: C80PracticeCodes_SurgeryGeneral,
  SurgeryNeurosurgery: C80PracticeCodes_SurgeryNeurosurgery,
  SurgeryPlasticSurgery: C80PracticeCodes_SurgeryPlasticSurgery,
  Urology: C80PracticeCodes_Urology,
  NuclearMedicine: C80PracticeCodes_NuclearMedicine,
  SurgicalSpecialtyOTHERNOTLISTED: C80PracticeCodes_SurgicalSpecialtyOTHERNOTLISTED,
  MedicalSpecialtyOTHERNOTLISTED: C80PracticeCodes_MedicalSpecialtyOTHERNOTLISTED,
  SurgeryTraumaAndOrthopedics: C80PracticeCodes_SurgeryTraumaAndOrthopedics,
  GeneralMedicine: C80PracticeCodes_GeneralMedicine,
  ClinicalHematology: C80PracticeCodes_ClinicalHematology,
  ClinicalCytogeneticsAndMolecularGenetics: C80PracticeCodes_ClinicalCytogeneticsAndMolecularGenetics,
  PalliativeMedicine: C80PracticeCodes_PalliativeMedicine,
  InfectiousDiseases: C80PracticeCodes_InfectiousDiseases,
  GenitoUrinaryMedicine: C80PracticeCodes_GenitoUrinaryMedicine,
  ClinicalNeuroPhysiology: C80PracticeCodes_ClinicalNeuroPhysiology,
  Rheumatology: C80PracticeCodes_Rheumatology,
  GeriatricMedicine: C80PracticeCodes_GeriatricMedicine,
  DentalMedicineSpecialties: C80PracticeCodes_DentalMedicineSpecialties,
  MedicalOphthalmology: C80PracticeCodes_MedicalOphthalmology,
  GeneralPractice: C80PracticeCodes_GeneralPractice,
  OccupationalMedicine: C80PracticeCodes_OccupationalMedicine,
  PainManagement: C80PracticeCodes_PainManagement,
  Psychotherapy: C80PracticeCodes_Psychotherapy,
  Radiology: C80PracticeCodes_Radiology,
  GeneralPathology: C80PracticeCodes_GeneralPathology,
  Hematopathology: C80PracticeCodes_Hematopathology,
  PublicHealthMedicine: C80PracticeCodes_PublicHealthMedicine,
  SurgeryDentalEndodontics: C80PracticeCodes_SurgeryDentalEndodontics,
  GeneralMedicalPractice: C80PracticeCodes_GeneralMedicalPractice,
  DentalGeneralDentalPractice: C80PracticeCodes_DentalGeneralDentalPractice,
  GynecologicalOncology: C80PracticeCodes_GynecologicalOncology,
  RespiteCare: C80PracticeCodes_RespiteCare,
  TropicalMedicine: C80PracticeCodes_TropicalMedicine,
  SurgeryDentistrySurgical: C80PracticeCodes_SurgeryDentistrySurgical,
  SleepStudies: C80PracticeCodes_SleepStudies,
  ClinicalMicrobiology: C80PracticeCodes_ClinicalMicrobiology,
  RadiologyInterventionalRadiology: C80PracticeCodes_RadiologyInterventionalRadiology,
  PediatricCardiology: C80PracticeCodes_PediatricCardiology,
  SurgeryDentalProstheticDentistryProsthodontics: C80PracticeCodes_SurgeryDentalProstheticDentistryProsthodontics,
  SurgeryDentalPeriodontalSurgery: C80PracticeCodes_SurgeryDentalPeriodontalSurgery,
  BurnsCare: C80PracticeCodes_BurnsCare,
  SurgeryVascular: C80PracticeCodes_SurgeryVascular,
  SurgeryColorectalSurgery: C80PracticeCodes_SurgeryColorectalSurgery,
  SurgeryDentalOralAndMaxillofacialSurgery: C80PracticeCodes_SurgeryDentalOralAndMaxillofacialSurgery,
  SurgeryCardiacSurgery: C80PracticeCodes_SurgeryCardiacSurgery,
  AdultMentalIllness: C80PracticeCodes_AdultMentalIllness,
  LearningDisability: C80PracticeCodes_LearningDisability,
  SurgeryBreastSurgery: C80PracticeCodes_SurgeryBreastSurgery,
  Obstetrics: C80PracticeCodes_Obstetrics,
  SurgeryCardiothoracicTransplantation: C80PracticeCodes_SurgeryCardiothoracicTransplantation,
  Hepatology: C80PracticeCodes_Hepatology,
  SurgeryHepatobiliaryAndPancreaticSurgery: C80PracticeCodes_SurgeryHepatobiliaryAndPancreaticSurgery,
  DiabeticMedicine: C80PracticeCodes_DiabeticMedicine,
  SurgeryBoneAndMarrowTransplantation: C80PracticeCodes_SurgeryBoneAndMarrowTransplantation,
  SurgeryTransplantationSurgery: C80PracticeCodes_SurgeryTransplantationSurgery,
  CriticalCareMedicine: C80PracticeCodes_CriticalCareMedicine,
  ClinicalImmunology: C80PracticeCodes_ClinicalImmunology,
  Toxicology: C80PracticeCodes_Toxicology,
  PreventiveMedicine: C80PracticeCodes_PreventiveMedicine,
  MilitaryMedicine: C80PracticeCodes_MilitaryMedicine,
  DiveMedicine: C80PracticeCodes_DiveMedicine,
  OsteopathicManipulativeMedicine: C80PracticeCodes_OsteopathicManipulativeMedicine,
  PediatricOncology: C80PracticeCodes_PediatricOncology,
  SurgeryDermatologicSurgery: C80PracticeCodes_SurgeryDermatologicSurgery,
  PediatricGastroenterology: C80PracticeCodes_PediatricGastroenterology,
  PulmonaryMedicine: C80PracticeCodes_PulmonaryMedicine,
  PediatricImmunology: C80PracticeCodes_PediatricImmunology,
  PediatricHematology: C80PracticeCodes_PediatricHematology,
  PediatricInfectiousDiseases: C80PracticeCodes_PediatricInfectiousDiseases,
  Otolaryngology: C80PracticeCodes_Otolaryngology,
  UrologicalOncology: C80PracticeCodes_UrologicalOncology,
  PediatricPulmonology: C80PracticeCodes_PediatricPulmonology,
  InternalMedicine: C80PracticeCodes_InternalMedicine,
  SurgicalOncology: C80PracticeCodes_SurgicalOncology,
  PediatricNephrology: C80PracticeCodes_PediatricNephrology,
  PediatricRheumatology: C80PracticeCodes_PediatricRheumatology,
  PediatricEndocrinology: C80PracticeCodes_PediatricEndocrinology,
  FamilyPractice: C80PracticeCodes_FamilyPractice,
  RadiationOncology: C80PracticeCodes_RadiationOncology,
  PediatricOphthalmology: C80PracticeCodes_PediatricOphthalmology,
  PediatricSurgeryBoneMarrowTransplantation: C80PracticeCodes_PediatricSurgeryBoneMarrowTransplantation,
  PediatricGenetics: C80PracticeCodes_PediatricGenetics,
  BloodBankingAndTransfusionMedicine: C80PracticeCodes_BloodBankingAndTransfusionMedicine,
  OphthalmicSurgery: C80PracticeCodes_OphthalmicSurgery,
};
const capability_statement_kind_Capability: Coding = {
  code: 'capability',
  display: 'Capability',
  system: 'http://hl7.org/fhir/capability-statement-kind',
};
const capability_statement_kind_Instance: Coding = {
  code: 'instance',
  display: 'Instance',
  system: 'http://hl7.org/fhir/capability-statement-kind',
};
const capability_statement_kind_Requirements: Coding = {
  code: 'requirements',
  display: 'Requirements',
  system: 'http://hl7.org/fhir/capability-statement-kind',
};
/**
 * How a capability statement is intended to be used.
 */
export const CapabilityStatementKind = {
  /**
   * The CapabilityStatement instance represents the capabilities of a system or piece of software, independent of a particular installation.
   */
  Capability: capability_statement_kind_Capability,
  /**
   * The CapabilityStatement instance represents the present capabilities of a specific system instance.  This is the kind returned by /metadata for a FHIR server end-point.
   */
  Instance: capability_statement_kind_Instance,
  /**
   * The CapabilityStatement instance represents a set of requirements for other systems to meet; e.g. as part of an implementation guide or 'request for proposal'.
   */
  Requirements: capability_statement_kind_Requirements,
};
/**
 * Resource types defined as part of FHIR that can be represented as in-line definitions of a care plan activity.
 */
export const CarePlanActivityKind = {
  /**
   * A booking of a healthcare event among patient(s), practitioner(s), related person(s) and/or device(s) for a specific date/time. This may result in one or more Encounter(s).
   */
  Appointment: resource_types_Appointment,
  /**
   * A request to convey information; e.g. the CDS system proposes that an alert be sent to a responsible provider, the CDS system proposes that the public health agency be notified about a reportable condition.
   */
  CommunicationRequest: resource_types_CommunicationRequest,
  /**
   * Represents a request for a patient to employ a medical device. The device may be an implantable device, or an external assistive device, such as a walker.
   */
  DeviceRequest: resource_types_DeviceRequest,
  /**
   * An order or request for both supply of the medication and the instructions for administration of the medication to a patient. The resource is called "MedicationRequest" rather than "MedicationPrescription" or "MedicationOrder" to generalize the use across inpatient and outpatient settings, including care plans, etc., and to harmonize with workflow patterns.
   */
  MedicationRequest: resource_types_MedicationRequest,
  /**
   * A request to supply a diet, formula feeding (enteral) or oral nutritional supplement to a patient/resident.
   */
  NutritionOrder: resource_types_NutritionOrder,
  /**
   * A record of a request for service such as diagnostic investigations, treatments, or operations to be performed.
   */
  ServiceRequest: resource_types_ServiceRequest,
  /**
   * A task to be performed.
   */
  Task: resource_types_Task,
  /**
   * An authorization for the provision of glasses and/or contact lenses to a patient.
   */
  VisionPrescription: resource_types_VisionPrescription,
};
const care_plan_activity_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/care-plan-activity-status',
};
const care_plan_activity_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/care-plan-activity-status',
};
const care_plan_activity_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/care-plan-activity-status',
};
const care_plan_activity_status_InProgress: Coding = {
  code: 'in-progress',
  display: 'In Progress',
  system: 'http://hl7.org/fhir/care-plan-activity-status',
};
const care_plan_activity_status_NotStarted: Coding = {
  code: 'not-started',
  display: 'Not Started',
  system: 'http://hl7.org/fhir/care-plan-activity-status',
};
const care_plan_activity_status_OnHold: Coding = {
  code: 'on-hold',
  display: 'On Hold',
  system: 'http://hl7.org/fhir/care-plan-activity-status',
};
const care_plan_activity_status_Scheduled: Coding = {
  code: 'scheduled',
  display: 'Scheduled',
  system: 'http://hl7.org/fhir/care-plan-activity-status',
};
const care_plan_activity_status_Stopped: Coding = {
  code: 'stopped',
  display: 'Stopped',
  system: 'http://hl7.org/fhir/care-plan-activity-status',
};
const care_plan_activity_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/care-plan-activity-status',
};
/**
 * Codes that reflect the current state of a care plan activity within its overall life cycle.
 */
export const CarePlanActivityStatus = {
  /**
   * The planned care plan activity has been withdrawn.
   */
  Cancelled: care_plan_activity_status_Cancelled,
  /**
   * Care plan activity has been completed (more or less) as planned.
   */
  Completed: care_plan_activity_status_Completed,
  /**
   * Care plan activity was entered in error and voided.
   */
  EnteredInError: care_plan_activity_status_EnteredInError,
  /**
   * Care plan activity has been started but is not yet complete.
   */
  InProgress: care_plan_activity_status_InProgress,
  /**
   * Care plan activity is planned but no action has yet been taken.
   */
  NotStarted: care_plan_activity_status_NotStarted,
  /**
   * Care plan activity was started but has temporarily ceased with an expectation of resumption at a future time.
   */
  OnHold: care_plan_activity_status_OnHold,
  /**
   * Appointment or other booking has occurred but activity has not yet begun.
   */
  Scheduled: care_plan_activity_status_Scheduled,
  /**
   * The planned care plan activity has been ended prior to completion after the activity was started.
   */
  Stopped: care_plan_activity_status_Stopped,
  /**
   * The current state of the care plan activity is not known.  Note: This concept is not to be used for "other" - one of the listed statuses is presumed to apply, but the authoring/source system does not know which one.
   */
  Unknown: care_plan_activity_status_Unknown,
};
const request_intent_Option: Coding = {
  code: 'option',
  display: 'Option',
  system: 'http://hl7.org/fhir/request-intent',
};
const request_intent_Order: Coding = {
  code: 'order',
  display: 'Order',
  system: 'http://hl7.org/fhir/request-intent',
};
const request_intent_Plan: Coding = {
  code: 'plan',
  display: 'Plan',
  system: 'http://hl7.org/fhir/request-intent',
};
const request_intent_Proposal: Coding = {
  code: 'proposal',
  display: 'Proposal',
  system: 'http://hl7.org/fhir/request-intent',
};
/**
 * Codes indicating the degree of authority/intentionality associated with a care plan.
 */
export const CarePlanIntent = {
  /**
   * The request represents a component or option for a RequestGroup that establishes timing, conditionality and/or other constraints among a set of requests.  Refer to [[[RequestGroup]]] for additional information on how this status is used.
   */
  Option: request_intent_Option,
  /**
   * The request represents a request/demand and authorization for action by a Practitioner.
   */
  Order: request_intent_Order,
  /**
   * The request represents an intention to ensure something occurs without providing an authorization for others to act.
   */
  Plan: request_intent_Plan,
  /**
   * The request is a suggestion made by someone/something that does not have an intention to ensure it occurs and without providing an authorization to act.
   */
  Proposal: request_intent_Proposal,
};
const care_team_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/care-team-status',
};
const care_team_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/care-team-status',
};
const care_team_status_Inactive: Coding = {
  code: 'inactive',
  display: 'Inactive',
  system: 'http://hl7.org/fhir/care-team-status',
};
const care_team_status_Proposed: Coding = {
  code: 'proposed',
  display: 'Proposed',
  system: 'http://hl7.org/fhir/care-team-status',
};
const care_team_status_Suspended: Coding = {
  code: 'suspended',
  display: 'Suspended',
  system: 'http://hl7.org/fhir/care-team-status',
};
/**
 * Indicates the status of the care team.
 */
export const CareTeamStatus = {
  /**
   * The care team is currently participating in the coordination and delivery of care.
   */
  Active: care_team_status_Active,
  /**
   * The care team should have never existed.
   */
  EnteredInError: care_team_status_EnteredInError,
  /**
   * The care team was, but is no longer, participating in the coordination and delivery of care.
   */
  Inactive: care_team_status_Inactive,
  /**
   * The care team has been drafted and proposed, but not yet participating in the coordination and delivery of patient care.
   */
  Proposed: care_team_status_Proposed,
  /**
   * The care team is temporarily on hold or suspended and not participating in the coordination and delivery of care.
   */
  Suspended: care_team_status_Suspended,
};
const catalogType_DeviceCatalog: Coding = {
  code: 'device',
  display: 'Device Catalog',
  system: 'http://terminology.hl7.org/CodeSystem/catalogType',
};
const catalogType_MedicationCatalog: Coding = {
  code: 'medication',
  display: 'Medication Catalog',
  system: 'http://terminology.hl7.org/CodeSystem/catalogType',
};
const catalogType_ProtocolList: Coding = {
  code: 'protocol',
  display: 'Protocol List',
  system: 'http://terminology.hl7.org/CodeSystem/catalogType',
};
/**
 * The type of catalog.
 */
export const CatalogType = {
  /**
   * Device Catalog.
   */
  DeviceCatalog: catalogType_DeviceCatalog,
  /**
   * Medication Catalog.
   */
  MedicationCatalog: catalogType_MedicationCatalog,
  /**
   * Protocol List.
   */
  ProtocolList: catalogType_ProtocolList,
};
const cdshooks_indicator_TheResponseIsCriticalAndIndicatesTheWorkflowShouldNotBeAllowedToProceed: Coding = {
  code: 'critical',
  display: 'The response is critical and indicates the workflow should not be allowed to proceed',
  system: 'http://cds-hooks.hl7.org/CodeSystem/indicator',
};
const cdshooks_indicator_TheResponseIsInformational: Coding = {
  code: 'info',
  display: 'The response is informational',
  system: 'http://cds-hooks.hl7.org/CodeSystem/indicator',
};
const cdshooks_indicator_TheResponseIsAWarning: Coding = {
  code: 'warning',
  display: 'The response is a warning',
  system: 'http://cds-hooks.hl7.org/CodeSystem/indicator',
};
/**
 * This value set captures the set of indicator codes defined by the CDS Hooks specification.
 */
export const CdshooksIndicator = {
  TheResponseIsCriticalAndIndicatesTheWorkflowShouldNotBeAllowedToProceed: cdshooks_indicator_TheResponseIsCriticalAndIndicatesTheWorkflowShouldNotBeAllowedToProceed,
  TheResponseIsInformational: cdshooks_indicator_TheResponseIsInformational,
  TheResponseIsAWarning: cdshooks_indicator_TheResponseIsAWarning,
};
const certainty_subcomponent_rating_Absent: Coding = {
  code: 'absent',
  display: 'absent',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-rating',
};
const certainty_subcomponent_rating_CriticalConcern: Coding = {
  code: 'critical-concern',
  display: 'critical concern',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-rating',
};
const certainty_subcomponent_rating_ReduceRating1: Coding = {
  code: 'downcode1',
  display: 'reduce rating: -1',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-rating',
};
const certainty_subcomponent_rating_ReduceRating2: Coding = {
  code: 'downcode2',
  display: 'reduce rating: -2',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-rating',
};
const certainty_subcomponent_rating_ReduceRating3: Coding = {
  code: 'downcode3',
  display: 'reduce rating: -3',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-rating',
};
const certainty_subcomponent_rating_NoChangeToRating: Coding = {
  code: 'no-change',
  display: 'no change to rating',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-rating',
};
const certainty_subcomponent_rating_NoSeriousConcern: Coding = {
  code: 'no-concern',
  display: 'no serious concern',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-rating',
};
const certainty_subcomponent_rating_Present: Coding = {
  code: 'present',
  display: 'present',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-rating',
};
const certainty_subcomponent_rating_SeriousConcern: Coding = {
  code: 'serious-concern',
  display: 'serious concern',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-rating',
};
const certainty_subcomponent_rating_IncreaseRatingPlus1: Coding = {
  code: 'upcode1',
  display: 'increase rating: +1',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-rating',
};
const certainty_subcomponent_rating_IncreaseRatingPlus2: Coding = {
  code: 'upcode2',
  display: 'increase rating: +2',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-rating',
};
/**
 * The quality rating of the subcomponent of a quality of evidence rating.
 */
export const CertaintySubcomponentRating = {
  /**
   * possible reason for increasing quality rating was checked and found to be absent.
   */
  Absent: certainty_subcomponent_rating_Absent,
  /**
   * critical concern.
   */
  CriticalConcern: certainty_subcomponent_rating_CriticalConcern,
  /**
   * reduce quality rating by 1.
   */
  ReduceRating1: certainty_subcomponent_rating_ReduceRating1,
  /**
   * reduce quality rating by 2.
   */
  ReduceRating2: certainty_subcomponent_rating_ReduceRating2,
  /**
   * reduce quality rating by 3.
   */
  ReduceRating3: certainty_subcomponent_rating_ReduceRating3,
  /**
   * no change to quality rating.
   */
  NoChangeToRating: certainty_subcomponent_rating_NoChangeToRating,
  /**
   * no serious concern.
   */
  NoSeriousConcern: certainty_subcomponent_rating_NoSeriousConcern,
  /**
   * possible reason for increasing quality rating was checked and found to bepresent.
   */
  Present: certainty_subcomponent_rating_Present,
  /**
   * serious concern.
   */
  SeriousConcern: certainty_subcomponent_rating_SeriousConcern,
  /**
   * increase quality rating by 1.
   */
  IncreaseRatingPlus1: certainty_subcomponent_rating_IncreaseRatingPlus1,
  /**
   * increase quality rating by 2.
   */
  IncreaseRatingPlus2: certainty_subcomponent_rating_IncreaseRatingPlus2,
};
const certainty_subcomponent_type_DoseResponseGradient: Coding = {
  code: 'DoseResponseGradient',
  display: 'Dose response gradient',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-type',
};
const certainty_subcomponent_type_Imprecision: Coding = {
  code: 'Imprecision',
  display: 'Imprecision',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-type',
};
const certainty_subcomponent_type_Inconsistency: Coding = {
  code: 'Inconsistency',
  display: 'Inconsistency',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-type',
};
const certainty_subcomponent_type_Indirectness: Coding = {
  code: 'Indirectness',
  display: 'Indirectness',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-type',
};
const certainty_subcomponent_type_LargeEffect: Coding = {
  code: 'LargeEffect',
  display: 'Large effect',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-type',
};
const certainty_subcomponent_type_PlausibleConfounding: Coding = {
  code: 'PlausibleConfounding',
  display: 'Plausible confounding',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-type',
};
const certainty_subcomponent_type_PublicationBias: Coding = {
  code: 'PublicationBias',
  display: 'Publication bias',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-type',
};
const certainty_subcomponent_type_RiskOfBias: Coding = {
  code: 'RiskOfBias',
  display: 'Risk of bias',
  system: 'http://terminology.hl7.org/CodeSystem/certainty-subcomponent-type',
};
/**
 * The subcomponent classification of quality of evidence rating systems.
 */
export const CertaintySubcomponentType = {
  /**
   * higher certainty due to dose response relationship.
   */
  DoseResponseGradient: certainty_subcomponent_type_DoseResponseGradient,
  /**
   * High quality evidence.
   */
  Imprecision: certainty_subcomponent_type_Imprecision,
  /**
   * concerns that findings are not similar enough to support certainty.
   */
  Inconsistency: certainty_subcomponent_type_Inconsistency,
  /**
   * concerns reducing external validity.
   */
  Indirectness: certainty_subcomponent_type_Indirectness,
  /**
   * higher certainty due to large effect size.
   */
  LargeEffect: certainty_subcomponent_type_LargeEffect,
  /**
   * higher certainty due to risk of bias in opposite direction.
   */
  PlausibleConfounding: certainty_subcomponent_type_PlausibleConfounding,
  /**
   * likelihood that what is published misrepresents what is available to publish.
   */
  PublicationBias: certainty_subcomponent_type_PublicationBias,
  /**
   * methodologic concerns reducing internal validity.
   */
  RiskOfBias: certainty_subcomponent_type_RiskOfBias,
};
const chargeitem_billingcodes_UnvorhergeseheneInanspruchnahme: Coding = {
  code: '1100',
  display: 'Unvorhergesehene Inanspruchnahme',
  system: 'http://terminology.hl7.org/CodeSystem/chargeitem-billingcodes',
};
const chargeitem_billingcodes_Notfallpauschale: Coding = {
  code: '1210',
  display: 'Notfallpauschale',
  system: 'http://terminology.hl7.org/CodeSystem/chargeitem-billingcodes',
};
const chargeitem_billingcodes_Grundpauschale: Coding = {
  code: '1320',
  display: 'Grundpauschale',
  system: 'http://terminology.hl7.org/CodeSystem/chargeitem-billingcodes',
};
/**
 * Example set of codes that can be used for billing purposes.
 */
export const ChargeitemBillingcodes = {
  /**
   * From German EBM billing system:
   * Unvorhergesehene Inanspruchnahme des Vertragsarztes durch einen Patienten;zwischen 19:00 und 22:00 Uhr;an Samstagen, Sonntagen und gesetzlichen Feiertagen, am 24.12. und 31.12. zwischen 07:00 und 19:00 Uhr.
   */
  UnvorhergeseheneInanspruchnahme: chargeitem_billingcodes_UnvorhergeseheneInanspruchnahme,
  /**
   * From German EBM billing system:
   * Notfallpauschale im organisierten Not(-fall)dienst und für nicht an der vertragsärztlichen Versorgung teilnehmende Ärzte, Institute und Krankenhäuser bei Inanspruchnahme;zwischen 07:00 und 19:00 Uhr.
   */
  Notfallpauschale: chargeitem_billingcodes_Notfallpauschale,
  /**
   * From German EBM billing system:
   * Grundpauschale für Ärzte, Institute und Krankenhäuser, die zur Erbringung von Leistungen innerhalb mindestens eines der Fachgebiete Anästhesiologie, Frauenheilkunde und Geburtshilfe, Haut- und Geschlechtskrankheiten, Mund-, Kiefer- und Gesichtschirurgie und Humangenetik ermächtigt sind.
   */
  Grundpauschale: chargeitem_billingcodes_Grundpauschale,
};
const chargeitem_status_Aborted: Coding = {
  code: 'aborted',
  display: 'Aborted',
  system: 'http://hl7.org/fhir/chargeitem-status',
};
const chargeitem_status_Billable: Coding = {
  code: 'billable',
  display: 'Billable',
  system: 'http://hl7.org/fhir/chargeitem-status',
};
const chargeitem_status_Billed: Coding = {
  code: 'billed',
  display: 'Billed',
  system: 'http://hl7.org/fhir/chargeitem-status',
};
const chargeitem_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/chargeitem-status',
};
const chargeitem_status_NotBillable: Coding = {
  code: 'not-billable',
  display: 'Not billable',
  system: 'http://hl7.org/fhir/chargeitem-status',
};
const chargeitem_status_Planned: Coding = {
  code: 'planned',
  display: 'Planned',
  system: 'http://hl7.org/fhir/chargeitem-status',
};
const chargeitem_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/chargeitem-status',
};
/**
 * Codes identifying the lifecycle stage of a ChargeItem.
 */
export const ChargeitemStatus = {
  /**
   * The processing of the charge was aborted.
   */
  Aborted: chargeitem_status_Aborted,
  /**
   * The charge item is ready for billing.
   */
  Billable: chargeitem_status_Billable,
  /**
   * The charge item has been billed (e.g. a billing engine has generated financial transactions by applying the associated ruled for the charge item to the context of the Encounter, and placed them into Claims/Invoices.
   */
  Billed: chargeitem_status_Billed,
  /**
   * The charge item has been entered in error and should not be processed for billing.
   */
  EnteredInError: chargeitem_status_EnteredInError,
  /**
   * The charge item has been determined to be not billable (e.g. due to rules associated with the billing code).
   */
  NotBillable: chargeitem_status_NotBillable,
  /**
   * The charge item has been entered, but the charged service is not  yet complete, so it shall not be billed yet but might be used in the context of pre-authorization.
   */
  Planned: chargeitem_status_Planned,
  /**
   * The authoring system does not know which of the status values currently applies for this charge item  Note: This concept is not to be used for "other" - one of the listed statuses is presumed to apply, it's just not known which one.
   */
  Unknown: chargeitem_status_Unknown,
};
const choice_list_orientation_Horizontal: Coding = {
  code: 'horizontal',
  display: 'Horizontal',
  system: 'http://terminology.hl7.org/CodeSystem/choice-list-orientation',
};
const choice_list_orientation_Vertical: Coding = {
  code: 'vertical',
  display: 'Vertical',
  system: 'http://terminology.hl7.org/CodeSystem/choice-list-orientation',
};
/**
 * Direction in which lists of possible answers should be displayed.
 */
export const ChoiceListOrientation = {
  /**
   * List choices along the horizontal axis.
   */
  Horizontal: choice_list_orientation_Horizontal,
  /**
   * List choices down the vertical axis.
   */
  Vertical: choice_list_orientation_Vertical,
};
const chromosome_human_Chromosome1: Coding = {
  code: '1',
  display: 'chromosome 1',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome10: Coding = {
  code: '10',
  display: 'chromosome 10',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome11: Coding = {
  code: '11',
  display: 'chromosome 11',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome12: Coding = {
  code: '12',
  display: 'chromosome 12',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome13: Coding = {
  code: '13',
  display: 'chromosome 13',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome14: Coding = {
  code: '14',
  display: 'chromosome 14',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome15: Coding = {
  code: '15',
  display: 'chromosome 15',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome16: Coding = {
  code: '16',
  display: 'chromosome 16',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome17: Coding = {
  code: '17',
  display: 'chromosome 17',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome18: Coding = {
  code: '18',
  display: 'chromosome 18',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome19: Coding = {
  code: '19',
  display: 'chromosome 19',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome2: Coding = {
  code: '2',
  display: 'chromosome 2',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome20: Coding = {
  code: '20',
  display: 'chromosome 20',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome21: Coding = {
  code: '21',
  display: 'chromosome 21',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome22: Coding = {
  code: '22',
  display: 'chromosome 22',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome3: Coding = {
  code: '3',
  display: 'chromosome 3',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome4: Coding = {
  code: '4',
  display: 'chromosome 4',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome5: Coding = {
  code: '5',
  display: 'chromosome 5',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome6: Coding = {
  code: '6',
  display: 'chromosome 6',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome7: Coding = {
  code: '7',
  display: 'chromosome 7',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome8: Coding = {
  code: '8',
  display: 'chromosome 8',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_Chromosome9: Coding = {
  code: '9',
  display: 'chromosome 9',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_ChromosomeX: Coding = {
  code: 'X',
  display: 'chromosome X',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
const chromosome_human_ChromosomeY: Coding = {
  code: 'Y',
  display: 'chromosome Y',
  system: 'http://terminology.hl7.org/CodeSystem/chromosome-human',
};
/**
 * Chromosome number for human.
 */
export const ChromosomeHuman = {
  /**
   * chromosome 1.
   */
  Chromosome1: chromosome_human_Chromosome1,
  /**
   * chromosome 10.
   */
  Chromosome10: chromosome_human_Chromosome10,
  /**
   * chromosome 11.
   */
  Chromosome11: chromosome_human_Chromosome11,
  /**
   * chromosome 12.
   */
  Chromosome12: chromosome_human_Chromosome12,
  /**
   * chromosome 13.
   */
  Chromosome13: chromosome_human_Chromosome13,
  /**
   * chromosome 14.
   */
  Chromosome14: chromosome_human_Chromosome14,
  /**
   * chromosome 15.
   */
  Chromosome15: chromosome_human_Chromosome15,
  /**
   * chromosome 16.
   */
  Chromosome16: chromosome_human_Chromosome16,
  /**
   * chromosome 17.
   */
  Chromosome17: chromosome_human_Chromosome17,
  /**
   * chromosome 18.
   */
  Chromosome18: chromosome_human_Chromosome18,
  /**
   * chromosome 19.
   */
  Chromosome19: chromosome_human_Chromosome19,
  /**
   * chromosome 2.
   */
  Chromosome2: chromosome_human_Chromosome2,
  /**
   * chromosome 20.
   */
  Chromosome20: chromosome_human_Chromosome20,
  /**
   * chromosome 21.
   */
  Chromosome21: chromosome_human_Chromosome21,
  /**
   * chromosome 22.
   */
  Chromosome22: chromosome_human_Chromosome22,
  /**
   * chromosome 3.
   */
  Chromosome3: chromosome_human_Chromosome3,
  /**
   * chromosome 4.
   */
  Chromosome4: chromosome_human_Chromosome4,
  /**
   * chromosome 5.
   */
  Chromosome5: chromosome_human_Chromosome5,
  /**
   * chromosome 6.
   */
  Chromosome6: chromosome_human_Chromosome6,
  /**
   * chromosome 7.
   */
  Chromosome7: chromosome_human_Chromosome7,
  /**
   * chromosome 8.
   */
  Chromosome8: chromosome_human_Chromosome8,
  /**
   * chromosome 9.
   */
  Chromosome9: chromosome_human_Chromosome9,
  /**
   * chromosome X.
   */
  ChromosomeX: chromosome_human_ChromosomeX,
  /**
   * chromosome Y.
   */
  ChromosomeY: chromosome_human_ChromosomeY,
};
const claim_careteamrole_AssistingProvider: Coding = {
  code: 'assist',
  display: 'Assisting Provider',
  system: 'http://terminology.hl7.org/CodeSystem/claimcareteamrole',
};
const claim_careteamrole_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/claimcareteamrole',
};
const claim_careteamrole_PrimaryProvider: Coding = {
  code: 'primary',
  display: 'Primary provider',
  system: 'http://terminology.hl7.org/CodeSystem/claimcareteamrole',
};
const claim_careteamrole_SupervisingProvider: Coding = {
  code: 'supervisor',
  display: 'Supervising Provider',
  system: 'http://terminology.hl7.org/CodeSystem/claimcareteamrole',
};
/**
 * This value set includes sample Claim Care Team Role codes.
 */
export const ClaimCareteamrole = {
  /**
   * Assisting care provider.
   */
  AssistingProvider: claim_careteamrole_AssistingProvider,
  /**
   * Other role on the care team.
   */
  Other: claim_careteamrole_Other,
  /**
   * The primary care provider.
   */
  PrimaryProvider: claim_careteamrole_PrimaryProvider,
  /**
   * Supervising care provider.
   */
  SupervisingProvider: claim_careteamrole_SupervisingProvider,
};
const claim_exception_Disabled: Coding = {
  code: 'disabled',
  display: 'Disabled',
  system: 'http://terminology.hl7.org/CodeSystem/claim-exception',
};
const claim_exception_StudentFulltime: Coding = {
  code: 'student',
  display: 'Student (Fulltime)',
  system: 'http://terminology.hl7.org/CodeSystem/claim-exception',
};
/**
 * This value set includes sample Exception codes.
 */
export const ClaimException = {
  /**
   * Disabled
   */
  Disabled: claim_exception_Disabled,
  /**
   * Fulltime Student
   */
  StudentFulltime: claim_exception_StudentFulltime,
};
const claim_informationcategory_Attachment: Coding = {
  code: 'attachment',
  display: 'Attachment',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
const claim_informationcategory_Discharge: Coding = {
  code: 'discharge',
  display: 'Discharge',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
const claim_informationcategory_EmploymentImpacted: Coding = {
  code: 'employmentimpacted',
  display: 'EmploymentImpacted',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
const claim_informationcategory_Exception: Coding = {
  code: 'exception',
  display: 'Exception',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
const claim_informationcategory_ExternalCaause: Coding = {
  code: 'externalcause',
  display: 'External Caause',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
const claim_informationcategory_Hospitalized: Coding = {
  code: 'hospitalized',
  display: 'Hospitalized',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
const claim_informationcategory_Information: Coding = {
  code: 'info',
  display: 'Information',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
const claim_informationcategory_MaterialsForwarded: Coding = {
  code: 'material',
  display: 'Materials Forwarded',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
const claim_informationcategory_MissingTooth: Coding = {
  code: 'missingtooth',
  display: 'Missing Tooth',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
const claim_informationcategory_Onset: Coding = {
  code: 'onset',
  display: 'Onset',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
const claim_informationcategory_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
const claim_informationcategory_PatientReasonForVisit: Coding = {
  code: 'patientreasonforvisit',
  display: 'Patient Reason for Visit',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
const claim_informationcategory_Prosthesis: Coding = {
  code: 'prosthesis',
  display: 'Prosthesis',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
const claim_informationcategory_RelatedServices: Coding = {
  code: 'related',
  display: 'Related Services',
  system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory',
};
/**
 * This value set includes sample Information Category codes.
 */
export const ClaimInformationcategory = {
  /**
   * Materials attached such as images, documents and resources.
   */
  Attachment: claim_informationcategory_Attachment,
  /**
   * Discharge status and discharge to locations.
   */
  Discharge: claim_informationcategory_Discharge,
  /**
   * An indication that the patient was unable to work, the period if known otherwise a Yes/No (boolean).
   */
  EmploymentImpacted: claim_informationcategory_EmploymentImpacted,
  /**
   * Insurance policy exceptions.
   */
  Exception: claim_informationcategory_Exception,
  /**
   * The external cause of an illness or injury.
   */
  ExternalCaause: claim_informationcategory_ExternalCaause,
  /**
   * An indication that the patient was hospitalized, the period if known otherwise a Yes/No (boolean).
   */
  Hospitalized: claim_informationcategory_Hospitalized,
  /**
   * Codes conveying additional situation and condition information.
   */
  Information: claim_informationcategory_Information,
  /**
   * Materials being forwarded, e.g. Models, molds, images, documents.
   */
  MaterialsForwarded: claim_informationcategory_MaterialsForwarded,
  /**
   * Teeth which are missing for any reason, for example: prior extraction, never developed.
   */
  MissingTooth: claim_informationcategory_MissingTooth,
  /**
   * Period, start or end dates of aspects of the Condition.
   */
  Onset: claim_informationcategory_Onset,
  /**
   * Other information identified by the type.system.
   */
  Other: claim_informationcategory_Other,
  /**
   * The reason for the patient visit.
   */
  PatientReasonForVisit: claim_informationcategory_PatientReasonForVisit,
  /**
   * The type of prosthesis and date of supply if a previously supplied prosthesis.
   */
  Prosthesis: claim_informationcategory_Prosthesis,
  /**
   * Nature and date of the related event e.g. Last exam, service, X-ray etc.
   */
  RelatedServices: claim_informationcategory_RelatedServices,
};
const claim_modifiers_RepairOfPriorServiceOrInstallation: Coding = {
  code: 'a',
  display: 'Repair of prior service or installation',
  system: 'http://terminology.hl7.org/CodeSystem/modifiers',
};
const claim_modifiers_TemporaryServiceOrInstallation: Coding = {
  code: 'b',
  display: 'Temporary service or installation',
  system: 'http://terminology.hl7.org/CodeSystem/modifiers',
};
const claim_modifiers_TMJTreatment: Coding = {
  code: 'c',
  display: 'TMJ treatment',
  system: 'http://terminology.hl7.org/CodeSystem/modifiers',
};
const claim_modifiers_ImplantOrAssociatedWithAnImplant: Coding = {
  code: 'e',
  display: 'Implant or associated with an implant',
  system: 'http://terminology.hl7.org/CodeSystem/modifiers',
};
const claim_modifiers_RushOrOutsideOfOfficeHours: Coding = {
  code: 'rooh',
  display: 'Rush or Outside of office hours',
  system: 'http://terminology.hl7.org/CodeSystem/modifiers',
};
const claim_modifiers_None: Coding = {
  code: 'x',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/modifiers',
};
/**
 * This value set includes sample Modifier type codes.
 */
export const ClaimModifiers = {
  /**
   * Repair of prior service or installation.
   */
  RepairOfPriorServiceOrInstallation: claim_modifiers_RepairOfPriorServiceOrInstallation,
  /**
   * Temporary service or installation.
   */
  TemporaryServiceOrInstallation: claim_modifiers_TemporaryServiceOrInstallation,
  /**
   * Treatment associated with TMJ.
   */
  TMJTreatment: claim_modifiers_TMJTreatment,
  /**
   * Implant or associated with an implant.
   */
  ImplantOrAssociatedWithAnImplant: claim_modifiers_ImplantOrAssociatedWithAnImplant,
  /**
   * A Rush service or service performed outside of normal office hours.
   */
  RushOrOutsideOfOfficeHours: claim_modifiers_RushOrOutsideOfOfficeHours,
  /**
   * None.
   */
  None: claim_modifiers_None,
};
const claim_subtype_EmergencyClaim: Coding = {
  code: 'emergency',
  display: 'Emergency Claim',
  system: 'http://terminology.hl7.org/CodeSystem/ex-claimsubtype',
};
const claim_subtype_OrthodonticClaim: Coding = {
  code: 'ortho',
  display: 'Orthodontic Claim',
  system: 'http://terminology.hl7.org/CodeSystem/ex-claimsubtype',
};
/**
 * This value set includes sample Claim SubType codes which are used to distinguish the claim types for example within type institutional there may be subtypes for emergency services, bed stay and transportation.
 */
export const ClaimSubtype = {
  /**
   * A claim for emergency services.
   */
  EmergencyClaim: claim_subtype_EmergencyClaim,
  /**
   * A claim for Orthodontic Services.
   */
  OrthodonticClaim: claim_subtype_OrthodonticClaim,
};
const claim_type_Institutional: Coding = {
  code: 'institutional',
  display: 'Institutional',
  system: 'http://terminology.hl7.org/CodeSystem/claim-type',
};
const claim_type_Oral: Coding = {
  code: 'oral',
  display: 'Oral',
  system: 'http://terminology.hl7.org/CodeSystem/claim-type',
};
const claim_type_Pharmacy: Coding = {
  code: 'pharmacy',
  display: 'Pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/claim-type',
};
const claim_type_Professional: Coding = {
  code: 'professional',
  display: 'Professional',
  system: 'http://terminology.hl7.org/CodeSystem/claim-type',
};
const claim_type_Vision: Coding = {
  code: 'vision',
  display: 'Vision',
  system: 'http://terminology.hl7.org/CodeSystem/claim-type',
};
/**
 * This value set includes Claim Type codes.
 */
export const ClaimType = {
  /**
   * Hospital, clinic and typically inpatient claims.
   */
  Institutional: claim_type_Institutional,
  /**
   * Dental, Denture and Hygiene claims.
   */
  Oral: claim_type_Oral,
  /**
   * Pharmacy claims for goods and services.
   */
  Pharmacy: claim_type_Pharmacy,
  /**
   * Typically, outpatient claims from Physician, Psychological, Chiropractor, Physiotherapy, Speech Pathology, rehabilitative, consulting.
   */
  Professional: claim_type_Professional,
  /**
   * Vision claims for professional services and products such as glasses and contact lenses.
   */
  Vision: claim_type_Vision,
};
const claim_use_Claim: Coding = {
  code: 'claim',
  display: 'Claim',
  system: 'http://hl7.org/fhir/claim-use',
};
const claim_use_Preauthorization: Coding = {
  code: 'preauthorization',
  display: 'Preauthorization',
  system: 'http://hl7.org/fhir/claim-use',
};
const claim_use_Predetermination: Coding = {
  code: 'predetermination',
  display: 'Predetermination',
  system: 'http://hl7.org/fhir/claim-use',
};
/**
 * The purpose of the Claim: predetermination, preauthorization, claim.
 */
export const ClaimUse = {
  /**
   * The treatment is complete and this represents a Claim for the services.
   */
  Claim: claim_use_Claim,
  /**
   * The treatment is proposed and this represents a Pre-authorization for the services.
   */
  Preauthorization: claim_use_Preauthorization,
  /**
   * The treatment is proposed and this represents a Pre-determination for the services.
   */
  Predetermination: claim_use_Predetermination,
};
const event_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/event-status',
};
const event_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/event-status',
};
const event_status_InProgress: Coding = {
  code: 'in-progress',
  display: 'In Progress',
  system: 'http://hl7.org/fhir/event-status',
};
/**
 * Codes that reflect the current state of a clinical impression within its overall lifecycle.
 */
export const ClinicalimpressionStatus = {
  /**
   * The event has now concluded.
   */
  Completed: event_status_Completed,
  /**
   * This electronic record should never have existed, though it is possible that real-world decisions were based on it.  (If real-world activity has occurred, the status should be "stopped" rather than "entered-in-error".).
   */
  EnteredInError: event_status_EnteredInError,
  /**
   * The event is currently occurring.
   */
  InProgress: event_status_InProgress,
};
const code_search_support_ImplicitCodes: Coding = {
  code: 'all',
  display: 'Implicit Codes',
  system: 'http://hl7.org/fhir/code-search-support',
};
const code_search_support_ExplicitCodes: Coding = {
  code: 'explicit',
  display: 'Explicit Codes',
  system: 'http://hl7.org/fhir/code-search-support',
};
/**
 * The degree to which the server supports the code search parameter on ValueSet, if it is supported.
 */
export const CodeSearchSupport = {
  /**
   * The search for code on ValueSet only includes all codes based on the expansion of the value set.
   */
  ImplicitCodes: code_search_support_ImplicitCodes,
  /**
   * The search for code on ValueSet only includes codes explicitly detailed on includes or expansions.
   */
  ExplicitCodes: code_search_support_ExplicitCodes,
};
const codesystem_altcode_kind_AlternateCode: Coding = {
  code: 'alternate',
  display: 'Alternate Code',
  system: 'http://terminology.hl7.org/CodeSystem/codesystem-altcode-kind',
};
const codesystem_altcode_kind_CaseInsensitive: Coding = {
  code: 'case-insensitive',
  display: 'Case Insensitive',
  system: 'http://terminology.hl7.org/CodeSystem/codesystem-altcode-kind',
};
const codesystem_altcode_kind_CaseSensitive: Coding = {
  code: 'case-sensitive',
  display: 'Case Sensitive',
  system: 'http://terminology.hl7.org/CodeSystem/codesystem-altcode-kind',
};
const codesystem_altcode_kind_Deprecated: Coding = {
  code: 'deprecated',
  display: 'Deprecated',
  system: 'http://terminology.hl7.org/CodeSystem/codesystem-altcode-kind',
};
const codesystem_altcode_kind_Expression: Coding = {
  code: 'expression',
  display: 'Expression',
  system: 'http://terminology.hl7.org/CodeSystem/codesystem-altcode-kind',
};
/**
 * Indicates the type of use for which the code is defined.
 */
export const CodesystemAltcodeKind = {
  /**
   * The code is an alternative code that can be used in any of the circumstances that the primary code can be used.
   */
  AlternateCode: codesystem_altcode_kind_AlternateCode,
  /**
   * The code is an alternative to be used when a case insensitive code is required (when the primary codes are case sensitive).
   */
  CaseInsensitive: codesystem_altcode_kind_CaseInsensitive,
  /**
   * The code is an alternative to be used when a case sensitive code is required (when the primary codes are case insensitive).
   */
  CaseSensitive: codesystem_altcode_kind_CaseSensitive,
  /**
   * The code should no longer be used, but was used in the past.
   */
  Deprecated: codesystem_altcode_kind_Deprecated,
  /**
   * The code is an alternative for the primary code that is built using the expression grammar defined by the code system.
   */
  Expression: codesystem_altcode_kind_Expression,
};
const codesystem_content_mode_Complete: Coding = {
  code: 'complete',
  display: 'Complete',
  system: 'http://hl7.org/fhir/codesystem-content-mode',
};
const codesystem_content_mode_Example: Coding = {
  code: 'example',
  display: 'Example',
  system: 'http://hl7.org/fhir/codesystem-content-mode',
};
const codesystem_content_mode_Fragment: Coding = {
  code: 'fragment',
  display: 'Fragment',
  system: 'http://hl7.org/fhir/codesystem-content-mode',
};
const codesystem_content_mode_NotPresent: Coding = {
  code: 'not-present',
  display: 'Not Present',
  system: 'http://hl7.org/fhir/codesystem-content-mode',
};
const codesystem_content_mode_Supplement: Coding = {
  code: 'supplement',
  display: 'Supplement',
  system: 'http://hl7.org/fhir/codesystem-content-mode',
};
/**
 * The extent of the content of the code system (the concepts and codes it defines) are represented in a code system resource.
 */
export const CodesystemContentMode = {
  /**
   * All the concepts defined by the code system are included in the code system resource.
   */
  Complete: codesystem_content_mode_Complete,
  /**
   * A few representative concepts are included in the code system resource. There is no useful intent in the subset choice and there's no process to make it workable: it's not intended to be workable.
   */
  Example: codesystem_content_mode_Example,
  /**
   * A subset of the code system concepts are included in the code system resource. This is a curated subset released for a specific purpose under the governance of the code system steward, and that the intent, bounds and consequences of the fragmentation are clearly defined in the fragment or the code system documentation. Fragments are also known as partitions.
   */
  Fragment: codesystem_content_mode_Fragment,
  /**
   * None of the concepts defined by the code system are included in the code system resource.
   */
  NotPresent: codesystem_content_mode_NotPresent,
  /**
   * The resource doesn't define any new concepts; it just provides additional designations and properties to another code system.
   */
  Supplement: codesystem_content_mode_Supplement,
};
const codesystem_hierarchy_meaning_ClassifiedWith: Coding = {
  code: 'classified-with',
  display: 'Classified With',
  system: 'http://hl7.org/fhir/codesystem-hierarchy-meaning',
};
const codesystem_hierarchy_meaning_GroupedBy: Coding = {
  code: 'grouped-by',
  display: 'Grouped By',
  system: 'http://hl7.org/fhir/codesystem-hierarchy-meaning',
};
const codesystem_hierarchy_meaning_IsA: Coding = {
  code: 'is-a',
  display: 'Is-A',
  system: 'http://hl7.org/fhir/codesystem-hierarchy-meaning',
};
const codesystem_hierarchy_meaning_PartOf: Coding = {
  code: 'part-of',
  display: 'Part Of',
  system: 'http://hl7.org/fhir/codesystem-hierarchy-meaning',
};
/**
 * The meaning of the hierarchy of concepts in a code system.
 */
export const CodesystemHierarchyMeaning = {
  /**
   * Child concepts in the hierarchy may have only one parent, and there is a presumption that the code system is a "closed world" meaning all things must be in the hierarchy. This results in concepts such as "not otherwise classified.".
   */
  ClassifiedWith: codesystem_hierarchy_meaning_ClassifiedWith,
  /**
   * No particular relationship between the concepts can be assumed, except what can be determined by inspection of the definitions of the elements (possible reasons to use this: importing from a source where this is not defined, or where various parts of the hierarchy have different meanings).
   */
  GroupedBy: codesystem_hierarchy_meaning_GroupedBy,
  /**
   * A hierarchy where the child concepts have an IS-A relationship with the parents - that is, all the properties of the parent are also true for its child concepts. Not that is-a is a property of the concepts, so additional subsumption relationships may be defined using properties or the [subsumes](extension-codesystem-subsumes.html) extension.
   */
  IsA: codesystem_hierarchy_meaning_IsA,
  /**
   * Child elements list the individual parts of a composite whole (e.g. body site).
   */
  PartOf: codesystem_hierarchy_meaning_PartOf,
};
const common_tags_Actionable: Coding = {
  code: 'actionable',
  display: 'Actionable',
  system: 'http://terminology.hl7.org/CodeSystem/common-tags',
};
/**
 * Common Tag Codes defined by FHIR project
 */
export const CommonTags = {
  /**
   * This request is intended to be acted upon, not merely stored
   */
  Actionable: common_tags_Actionable,
};
const communication_category_Alert: Coding = {
  code: 'alert',
  display: 'Alert',
  system: 'http://terminology.hl7.org/CodeSystem/communication-category',
};
const communication_category_Instruction: Coding = {
  code: 'instruction',
  display: 'Instruction',
  system: 'http://terminology.hl7.org/CodeSystem/communication-category',
};
const communication_category_Notification: Coding = {
  code: 'notification',
  display: 'Notification',
  system: 'http://terminology.hl7.org/CodeSystem/communication-category',
};
const communication_category_Reminder: Coding = {
  code: 'reminder',
  display: 'Reminder',
  system: 'http://terminology.hl7.org/CodeSystem/communication-category',
};
/**
 * Codes for general categories of communications such as alerts, instructions, etc.
 */
export const CommunicationCategory = {
  /**
   * The communication conveys an alert.
   */
  Alert: communication_category_Alert,
  /**
   * The communication conveys an instruction.
   */
  Instruction: communication_category_Instruction,
  /**
   * The communication conveys a notification.
   */
  Notification: communication_category_Notification,
  /**
   * The communication conveys a reminder.
   */
  Reminder: communication_category_Reminder,
};
const communication_not_done_reason_FamilyObjection: Coding = {
  code: 'family-objection',
  display: 'Family Objection',
  system: 'http://terminology.hl7.org/CodeSystem/communication-not-done-reason',
};
const communication_not_done_reason_InvalidPhoneNumber: Coding = {
  code: 'invalid-phone-number',
  display: 'Invalid Phone Number',
  system: 'http://terminology.hl7.org/CodeSystem/communication-not-done-reason',
};
const communication_not_done_reason_PatientObjection: Coding = {
  code: 'patient-objection',
  display: 'Patient Objection',
  system: 'http://terminology.hl7.org/CodeSystem/communication-not-done-reason',
};
const communication_not_done_reason_RecipientUnavailable: Coding = {
  code: 'recipient-unavailable',
  display: 'Recipient Unavailable',
  system: 'http://terminology.hl7.org/CodeSystem/communication-not-done-reason',
};
const communication_not_done_reason_SystemError: Coding = {
  code: 'system-error',
  display: 'System Error',
  system: 'http://terminology.hl7.org/CodeSystem/communication-not-done-reason',
};
const communication_not_done_reason_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/communication-not-done-reason',
};
/**
 * Codes for the reason why a communication did not happen.
 */
export const CommunicationNotDoneReason = {
  /**
   * The communication was not done due to a family objection.
   */
  FamilyObjection: communication_not_done_reason_FamilyObjection,
  /**
   * The communication was not done due to an invalid phone number.
   */
  InvalidPhoneNumber: communication_not_done_reason_InvalidPhoneNumber,
  /**
   * The communication was not done due to a patient objection.
   */
  PatientObjection: communication_not_done_reason_PatientObjection,
  /**
   * The communication was not done due to the recipient being unavailable.
   */
  RecipientUnavailable: communication_not_done_reason_RecipientUnavailable,
  /**
   * The communication was not done due to a system error.
   */
  SystemError: communication_not_done_reason_SystemError,
  /**
   * The communication was not done due to an unknown reason.
   */
  Unknown: communication_not_done_reason_Unknown,
};
const communication_topic_AppointmentReminder: Coding = {
  code: 'appointment-reminder',
  display: 'Appointment Reminder',
  system: 'http://terminology.hl7.org/CodeSystem/communication-topic',
};
const communication_topic_PhoneConsult: Coding = {
  code: 'phone-consult',
  display: 'Phone Consult',
  system: 'http://terminology.hl7.org/CodeSystem/communication-topic',
};
const communication_topic_PrescriptionRefillRequest: Coding = {
  code: 'prescription-refill-request',
  display: 'Prescription Refill Request',
  system: 'http://terminology.hl7.org/CodeSystem/communication-topic',
};
const communication_topic_ProgressUpdate: Coding = {
  code: 'progress-update',
  display: 'Progress Update',
  system: 'http://terminology.hl7.org/CodeSystem/communication-topic',
};
const communication_topic_ReportLabs: Coding = {
  code: 'report-labs',
  display: 'Report Labs',
  system: 'http://terminology.hl7.org/CodeSystem/communication-topic',
};
const communication_topic_SummaryReport: Coding = {
  code: 'summary-report',
  display: 'Summary Report',
  system: 'http://terminology.hl7.org/CodeSystem/communication-topic',
};
/**
 * Codes describing the purpose or content of the communication.
 */
export const CommunicationTopic = {
  /**
   * The purpose or content of the communication is an appointment reminder.
   */
  AppointmentReminder: communication_topic_AppointmentReminder,
  /**
   * The purpose or content of the communication is a phone consult.
   */
  PhoneConsult: communication_topic_PhoneConsult,
  /**
   * The purpose or content of the communication is a prescription refill request.
   */
  PrescriptionRefillRequest: communication_topic_PrescriptionRefillRequest,
  /**
   * The purpose or content of the communication is a progress update.
   */
  ProgressUpdate: communication_topic_ProgressUpdate,
  /**
   * The purpose or content of the communication is to report labs.
   */
  ReportLabs: communication_topic_ReportLabs,
  /**
   * The purpose or content of the communication is a summary report.
   */
  SummaryReport: communication_topic_SummaryReport,
};
const compartment_type_Device: Coding = {
  code: 'Device',
  display: 'Device',
  system: 'http://hl7.org/fhir/compartment-type',
};
const compartment_type_Encounter: Coding = {
  code: 'Encounter',
  display: 'Encounter',
  system: 'http://hl7.org/fhir/compartment-type',
};
const compartment_type_Patient: Coding = {
  code: 'Patient',
  display: 'Patient',
  system: 'http://hl7.org/fhir/compartment-type',
};
const compartment_type_Practitioner: Coding = {
  code: 'Practitioner',
  display: 'Practitioner',
  system: 'http://hl7.org/fhir/compartment-type',
};
const compartment_type_RelatedPerson: Coding = {
  code: 'RelatedPerson',
  display: 'RelatedPerson',
  system: 'http://hl7.org/fhir/compartment-type',
};
/**
 * Which type a compartment definition describes.
 */
export const CompartmentType = {
  /**
   * The compartment definition is for the device compartment.
   */
  Device: compartment_type_Device,
  /**
   * The compartment definition is for the encounter compartment.
   */
  Encounter: compartment_type_Encounter,
  /**
   * The compartment definition is for the patient compartment.
   */
  Patient: compartment_type_Patient,
  /**
   * The compartment definition is for the practitioner compartment.
   */
  Practitioner: compartment_type_Practitioner,
  /**
   * The compartment definition is for the related-person compartment.
   */
  RelatedPerson: compartment_type_RelatedPerson,
};
const composite_measure_scoring_AllOrNothing: Coding = {
  code: 'all-or-nothing',
  display: 'All-or-nothing',
  system: 'http://terminology.hl7.org/CodeSystem/composite-measure-scoring',
};
const composite_measure_scoring_Linear: Coding = {
  code: 'linear',
  display: 'Linear',
  system: 'http://terminology.hl7.org/CodeSystem/composite-measure-scoring',
};
const composite_measure_scoring_Opportunity: Coding = {
  code: 'opportunity',
  display: 'Opportunity',
  system: 'http://terminology.hl7.org/CodeSystem/composite-measure-scoring',
};
const composite_measure_scoring_Weighted: Coding = {
  code: 'weighted',
  display: 'Weighted',
  system: 'http://terminology.hl7.org/CodeSystem/composite-measure-scoring',
};
/**
 * The composite scoring method of the measure.
 */
export const CompositeMeasureScoring = {
  /**
   * All-or-nothing scoring includes an individual in the numerator of the composite measure if they are in the numerators of all of the component measures in which they are in the denominator.
   */
  AllOrNothing: composite_measure_scoring_AllOrNothing,
  /**
   * Linear scoring gives an individual a score based on the number of numerators in which they appear.
   */
  Linear: composite_measure_scoring_Linear,
  /**
   * Opportunity scoring combines the scores from component measures by combining the numerators and denominators for each component.
   */
  Opportunity: composite_measure_scoring_Opportunity,
  /**
   * Weighted scoring gives an individual a score based on a weighted factor for each component numerator in which they appear.
   */
  Weighted: composite_measure_scoring_Weighted,
};
const composition_altcode_kind_AlternateCode: Coding = {
  code: 'alternate',
  display: 'Alternate Code',
  system: 'http://terminology.hl7.org/CodeSystem/composition-altcode-kind',
};
const composition_altcode_kind_CaseInsensitive: Coding = {
  code: 'case-insensitive',
  display: 'Case Insensitive',
  system: 'http://terminology.hl7.org/CodeSystem/composition-altcode-kind',
};
const composition_altcode_kind_CaseSensitive: Coding = {
  code: 'case-sensitive',
  display: 'Case Sensitive',
  system: 'http://terminology.hl7.org/CodeSystem/composition-altcode-kind',
};
const composition_altcode_kind_Deprecated: Coding = {
  code: 'deprecated',
  display: 'Deprecated',
  system: 'http://terminology.hl7.org/CodeSystem/composition-altcode-kind',
};
const composition_altcode_kind_Expression: Coding = {
  code: 'expression',
  display: 'Expression',
  system: 'http://terminology.hl7.org/CodeSystem/composition-altcode-kind',
};
/**
 * Indicates the type of use for which the code is defined.
 */
export const CompositionAltcodeKind = {
  /**
   * The code is an alternative code that can be used in any of the circumstances that the primary code can be used.
   */
  AlternateCode: composition_altcode_kind_AlternateCode,
  /**
   * The code is an alternative to be used when a case insensitive code is required (when the primary codes are case sensitive).
   */
  CaseInsensitive: composition_altcode_kind_CaseInsensitive,
  /**
   * The code is an alternative to be used when a case sensitive code is required (when the primary codes are case insensitive).
   */
  CaseSensitive: composition_altcode_kind_CaseSensitive,
  /**
   * The code should no longer be used, but was used in the past.
   */
  Deprecated: composition_altcode_kind_Deprecated,
  /**
   * The code is an alternative for the primary code that is built using the expression grammar defined by the code system.
   */
  Expression: composition_altcode_kind_Expression,
};
const composition_attestation_mode_Legal: Coding = {
  code: 'legal',
  display: 'Legal',
  system: 'http://hl7.org/fhir/composition-attestation-mode',
};
const composition_attestation_mode_Official: Coding = {
  code: 'official',
  display: 'Official',
  system: 'http://hl7.org/fhir/composition-attestation-mode',
};
const composition_attestation_mode_Personal: Coding = {
  code: 'personal',
  display: 'Personal',
  system: 'http://hl7.org/fhir/composition-attestation-mode',
};
const composition_attestation_mode_Professional: Coding = {
  code: 'professional',
  display: 'Professional',
  system: 'http://hl7.org/fhir/composition-attestation-mode',
};
/**
 * The way in which a person authenticated a composition.
 */
export const CompositionAttestationMode = {
  /**
   * The person authenticated the content and accepted legal responsibility for its content.
   */
  Legal: composition_attestation_mode_Legal,
  /**
   * The organization authenticated the content as consistent with their policies and procedures.
   */
  Official: composition_attestation_mode_Official,
  /**
   * The person authenticated the content in their personal capacity.
   */
  Personal: composition_attestation_mode_Personal,
  /**
   * The person authenticated the content in their professional capacity.
   */
  Professional: composition_attestation_mode_Professional,
};
const composition_status_Amended: Coding = {
  code: 'amended',
  display: 'Amended',
  system: 'http://hl7.org/fhir/composition-status',
};
const composition_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/composition-status',
};
const composition_status_Final: Coding = {
  code: 'final',
  display: 'Final',
  system: 'http://hl7.org/fhir/composition-status',
};
const composition_status_Preliminary: Coding = {
  code: 'preliminary',
  display: 'Preliminary',
  system: 'http://hl7.org/fhir/composition-status',
};
/**
 * The workflow/clinical status of the composition.
 */
export const CompositionStatus = {
  /**
   * The composition content or the referenced resources have been modified (edited or added to) subsequent to being released as "final" and the composition is complete and verified by an authorized person.
   */
  Amended: composition_status_Amended,
  /**
   * The composition or document was originally created/issued in error, and this is an amendment that marks that the entire series should not be considered as valid.
   */
  EnteredInError: composition_status_EnteredInError,
  /**
   * This version of the composition is complete and verified by an appropriate person and no further work is planned. Any subsequent updates would be on a new version of the composition.
   */
  Final: composition_status_Final,
  /**
   * This is a preliminary composition or document (also known as initial or interim). The content may be incomplete or unverified.
   */
  Preliminary: composition_status_Preliminary,
};
const concept_map_equivalence_Disjoint: Coding = {
  code: 'disjoint',
  display: 'Disjoint',
  system: 'http://hl7.org/fhir/concept-map-equivalence',
};
const concept_map_equivalence_Equal: Coding = {
  code: 'equal',
  display: 'Equal',
  system: 'http://hl7.org/fhir/concept-map-equivalence',
};
const concept_map_equivalence_Equivalent: Coding = {
  code: 'equivalent',
  display: 'Equivalent',
  system: 'http://hl7.org/fhir/concept-map-equivalence',
};
const concept_map_equivalence_Inexact: Coding = {
  code: 'inexact',
  display: 'Inexact',
  system: 'http://hl7.org/fhir/concept-map-equivalence',
};
const concept_map_equivalence_Narrower: Coding = {
  code: 'narrower',
  display: 'Narrower',
  system: 'http://hl7.org/fhir/concept-map-equivalence',
};
const concept_map_equivalence_RelatedTo: Coding = {
  code: 'relatedto',
  display: 'Related To',
  system: 'http://hl7.org/fhir/concept-map-equivalence',
};
const concept_map_equivalence_Specializes: Coding = {
  code: 'specializes',
  display: 'Specializes',
  system: 'http://hl7.org/fhir/concept-map-equivalence',
};
const concept_map_equivalence_Subsumes: Coding = {
  code: 'subsumes',
  display: 'Subsumes',
  system: 'http://hl7.org/fhir/concept-map-equivalence',
};
const concept_map_equivalence_Unmatched: Coding = {
  code: 'unmatched',
  display: 'Unmatched',
  system: 'http://hl7.org/fhir/concept-map-equivalence',
};
const concept_map_equivalence_Wider: Coding = {
  code: 'wider',
  display: 'Wider',
  system: 'http://hl7.org/fhir/concept-map-equivalence',
};
/**
 * The degree of equivalence between concepts.
 */
export const ConceptMapEquivalence = {
  /**
   * This is an explicit assertion that there is no mapping between the source and target concept.
   */
  Disjoint: concept_map_equivalence_Disjoint,
  /**
   * The definitions of the concepts are exactly the same (i.e. only grammatical differences) and structural implications of meaning are identical or irrelevant (i.e. intentionally identical).
   */
  Equal: concept_map_equivalence_Equal,
  /**
   * The definitions of the concepts mean the same thing (including when structural implications of meaning are considered) (i.e. extensionally identical).
   */
  Equivalent: concept_map_equivalence_Equivalent,
  /**
   * The target mapping overlaps with the source concept, but both source and target cover additional meaning, or the definitions are imprecise and it is uncertain whether they have the same boundaries to their meaning. The sense in which the mapping is inexact SHALL be described in the comments in this case, and applications should be careful when attempting to use these mappings operationally.
   */
  Inexact: concept_map_equivalence_Inexact,
  /**
   * The target mapping is narrower in meaning than the source concept. The sense in which the mapping is narrower SHALL be described in the comments in this case, and applications should be careful when attempting to use these mappings operationally.
   */
  Narrower: concept_map_equivalence_Narrower,
  /**
   * The concepts are related to each other, and have at least some overlap in meaning, but the exact relationship is not known.
   */
  RelatedTo: concept_map_equivalence_RelatedTo,
  /**
   * The target mapping specializes the meaning of the source concept (e.g. the target is-a source).
   */
  Specializes: concept_map_equivalence_Specializes,
  /**
   * The target mapping subsumes the meaning of the source concept (e.g. the source is-a target).
   */
  Subsumes: concept_map_equivalence_Subsumes,
  /**
   * There is no match for this concept in the target code system.
   */
  Unmatched: concept_map_equivalence_Unmatched,
  /**
   * The target mapping is wider in meaning than the source concept.
   */
  Wider: concept_map_equivalence_Wider,
};
const conceptmap_unmapped_mode_FixedCode: Coding = {
  code: 'fixed',
  display: 'Fixed Code',
  system: 'http://hl7.org/fhir/conceptmap-unmapped-mode',
};
const conceptmap_unmapped_mode_OtherMap: Coding = {
  code: 'other-map',
  display: 'Other Map',
  system: 'http://hl7.org/fhir/conceptmap-unmapped-mode',
};
const conceptmap_unmapped_mode_ProvidedCode: Coding = {
  code: 'provided',
  display: 'Provided Code',
  system: 'http://hl7.org/fhir/conceptmap-unmapped-mode',
};
/**
 * Defines which action to take if there is no match in the group.
 */
export const ConceptmapUnmappedMode = {
  /**
   * Use the code explicitly provided in the group.unmapped.
   */
  FixedCode: conceptmap_unmapped_mode_FixedCode,
  /**
   * Use the map identified by the canonical URL in the url element.
   */
  OtherMap: conceptmap_unmapped_mode_OtherMap,
  /**
   * Use the code as provided in the $translate request.
   */
  ProvidedCode: conceptmap_unmapped_mode_ProvidedCode,
};
const concept_property_type_Boolean: Coding = {
  code: 'boolean',
  display: 'boolean',
  system: 'http://hl7.org/fhir/concept-property-type',
};
const concept_property_type_CodeInternalReference: Coding = {
  code: 'code',
  display: 'code (internal reference)',
  system: 'http://hl7.org/fhir/concept-property-type',
};
const concept_property_type_CodingExternalReference: Coding = {
  code: 'Coding',
  display: 'Coding (external reference)',
  system: 'http://hl7.org/fhir/concept-property-type',
};
const concept_property_type_DateTime: Coding = {
  code: 'dateTime',
  display: 'dateTime',
  system: 'http://hl7.org/fhir/concept-property-type',
};
const concept_property_type_Decimal: Coding = {
  code: 'decimal',
  display: 'decimal',
  system: 'http://hl7.org/fhir/concept-property-type',
};
const concept_property_type_Integer: Coding = {
  code: 'integer',
  display: 'integer',
  system: 'http://hl7.org/fhir/concept-property-type',
};
const concept_property_type_String: Coding = {
  code: 'string',
  display: 'string',
  system: 'http://hl7.org/fhir/concept-property-type',
};
/**
 * The type of a property value.
 */
export const ConceptPropertyType = {
  /**
   * The property value is a boolean true | false.
   */
  Boolean: concept_property_type_Boolean,
  /**
   * The property value is a code that identifies a concept defined in the code system.
   */
  CodeInternalReference: concept_property_type_CodeInternalReference,
  /**
   * The property  value is a code defined in an external code system. This may be used for translations, but is not the intent.
   */
  CodingExternalReference: concept_property_type_CodingExternalReference,
  /**
   * The property is a date or a date + time.
   */
  DateTime: concept_property_type_DateTime,
  /**
   * The property value is a decimal number.
   */
  Decimal: concept_property_type_Decimal,
  /**
   * The property value is a string (often used to assign ranking values to concepts for supporting score assessments).
   */
  Integer: concept_property_type_Integer,
  /**
   * The property value is a string.
   */
  String: concept_property_type_String,
};
const concept_subsumption_outcome_Equivalent: Coding = {
  code: 'equivalent',
  display: 'Equivalent',
  system: 'http://hl7.org/fhir/concept-subsumption-outcome',
};
const concept_subsumption_outcome_NotSubsumed: Coding = {
  code: 'not-subsumed',
  display: 'Not-Subsumed',
  system: 'http://hl7.org/fhir/concept-subsumption-outcome',
};
const concept_subsumption_outcome_SubsumedBy: Coding = {
  code: 'subsumed-by',
  display: 'Subsumed-By',
  system: 'http://hl7.org/fhir/concept-subsumption-outcome',
};
const concept_subsumption_outcome_Subsumes: Coding = {
  code: 'subsumes',
  display: 'Subsumes',
  system: 'http://hl7.org/fhir/concept-subsumption-outcome',
};
/**
 * The subsumption relationship between code/Coding "A" and code/Coding "B". There are 4 possible codes to be returned: equivalent, subsumes, subsumed-by, and not-subsumed. If the server is unable to determine the relationship between the codes/Codings, then it returns an error (i.e. an OperationOutcome).
 */
export const ConceptSubsumptionOutcome = {
  /**
   * The two concepts are equivalent (have the same properties).
   */
  Equivalent: concept_subsumption_outcome_Equivalent,
  /**
   * Coding/code "A" and Coding/code "B" are disjoint (e.g. each has propeties that the other doesn't have).
   */
  NotSubsumed: concept_subsumption_outcome_NotSubsumed,
  /**
   * Coding/code "A" is subsumed by Coding/code "B" (e.g. A has all the properties B has, and some of it's own).
   */
  SubsumedBy: concept_subsumption_outcome_SubsumedBy,
  /**
   * Coding/code "A" subsumes Coding/code "B" (e.g. B has all the properties A has, and some of it's own).
   */
  Subsumes: concept_subsumption_outcome_Subsumes,
};
const conditional_delete_status_MultipleDeletesSupported: Coding = {
  code: 'multiple',
  display: 'Multiple Deletes Supported',
  system: 'http://hl7.org/fhir/conditional-delete-status',
};
const conditional_delete_status_NotSupported: Coding = {
  code: 'not-supported',
  display: 'Not Supported',
  system: 'http://hl7.org/fhir/conditional-delete-status',
};
const conditional_delete_status_SingleDeletesSupported: Coding = {
  code: 'single',
  display: 'Single Deletes Supported',
  system: 'http://hl7.org/fhir/conditional-delete-status',
};
/**
 * A code that indicates how the server supports conditional delete.
 */
export const ConditionalDeleteStatus = {
  /**
   * Conditional deletes are supported, and multiple resources can be deleted in a single interaction.
   */
  MultipleDeletesSupported: conditional_delete_status_MultipleDeletesSupported,
  /**
   * No support for conditional deletes.
   */
  NotSupported: conditional_delete_status_NotSupported,
  /**
   * Conditional deletes are supported, but only single resources at a time.
   */
  SingleDeletesSupported: conditional_delete_status_SingleDeletesSupported,
};
const conditional_read_status_FullSupport: Coding = {
  code: 'full-support',
  display: 'Full Support',
  system: 'http://hl7.org/fhir/conditional-read-status',
};
const conditional_read_status_IfModifiedSince: Coding = {
  code: 'modified-since',
  display: 'If-Modified-Since',
  system: 'http://hl7.org/fhir/conditional-read-status',
};
const conditional_read_status_IfNoneMatch: Coding = {
  code: 'not-match',
  display: 'If-None-Match',
  system: 'http://hl7.org/fhir/conditional-read-status',
};
const conditional_read_status_NotSupported: Coding = {
  code: 'not-supported',
  display: 'Not Supported',
  system: 'http://hl7.org/fhir/conditional-read-status',
};
/**
 * A code that indicates how the server supports conditional read.
 */
export const ConditionalReadStatus = {
  /**
   * Conditional reads are supported, with both If-Modified-Since and If-None-Match HTTP Headers.
   */
  FullSupport: conditional_read_status_FullSupport,
  /**
   * Conditional reads are supported, but only with the If-Modified-Since HTTP Header.
   */
  IfModifiedSince: conditional_read_status_IfModifiedSince,
  /**
   * Conditional reads are supported, but only with the If-None-Match HTTP Header.
   */
  IfNoneMatch: conditional_read_status_IfNoneMatch,
  /**
   * No support for conditional reads.
   */
  NotSupported: conditional_read_status_NotSupported,
};
const condition_category_EncounterDiagnosis: Coding = {
  code: 'encounter-diagnosis',
  display: 'Encounter Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/condition-category',
};
const condition_category_ProblemListItem: Coding = {
  code: 'problem-list-item',
  display: 'Problem List Item',
  system: 'http://terminology.hl7.org/CodeSystem/condition-category',
};
/**
 * Preferred value set for Condition Categories.
 */
export const ConditionCategory = {
  /**
   * A point in time diagnosis (e.g. from a physician or nurse) in context of an encounter.
   */
  EncounterDiagnosis: condition_category_EncounterDiagnosis,
  /**
   * An item on a problem list that can be managed over time and can be expressed by a practitioner (e.g. physician, nurse), patient, or related person.
   */
  ProblemListItem: condition_category_ProblemListItem,
};
const condition_clinical_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
};
const condition_clinical_Inactive: Coding = {
  code: 'inactive',
  display: 'Inactive',
  system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
};
const condition_clinical_Recurrence: Coding = {
  code: 'recurrence',
  display: 'Recurrence',
  system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
};
const condition_clinical_Relapse: Coding = {
  code: 'relapse',
  display: 'Relapse',
  system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
};
const condition_clinical_Remission: Coding = {
  code: 'remission',
  display: 'Remission',
  system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
};
const condition_clinical_Resolved: Coding = {
  code: 'resolved',
  display: 'Resolved',
  system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
};
/**
 * Preferred value set for Condition Clinical Status.
 */
export const ConditionClinical = {
  /**
   * The subject is currently experiencing the symptoms of the condition or there is evidence of the condition.
   */
  Active: condition_clinical_Active,
  /**
   * The subject is no longer experiencing the symptoms of the condition or there is no longer evidence of the condition.
   */
  Inactive: condition_clinical_Inactive,
  /**
   * The subject is experiencing a re-occurence or repeating of a previously resolved condition, e.g. urinary tract infection, pancreatitis, cholangitis, conjunctivitis.
   */
  Recurrence: condition_clinical_Recurrence,
  /**
   * The subject is experiencing a return of a condition, or signs and symptoms after a period of improvement or remission, e.g. relapse of cancer, multiple sclerosis, rheumatoid arthritis, systemic lupus erythematosus, bipolar disorder, [psychotic relapse of] schizophrenia, etc.
   */
  Relapse: condition_clinical_Relapse,
  /**
   * The subject is no longer experiencing the symptoms of the condition, but there is a risk of the symptoms returning.
   */
  Remission: condition_clinical_Remission,
  /**
   * The subject is no longer experiencing the symptoms of the condition and there is a negligible perceived risk of the symptoms returning.
   */
  Resolved: condition_clinical_Resolved,
};
const ConditionCode_NoCurrentProblemsOrDisability: Coding = {
  code: '160245001',
  display: 'No current problems or disability',
  system: 'http://snomed.info/sct',
};
/**
 * Example value set for Condition/Problem/Diagnosis codes.
 */
export const ConditionCode = {
  NoCurrentProblemsOrDisability: ConditionCode_NoCurrentProblemsOrDisability,
};
const ConditionSeverity_VAL24484000: Coding = {
  code: '24484000',
  system: 'http://snomed.info/sct',
};
const ConditionSeverity_VAL255604002: Coding = {
  code: '255604002',
  system: 'http://snomed.info/sct',
};
const ConditionSeverity_VAL6736007: Coding = {
  code: '6736007',
  system: 'http://snomed.info/sct',
};
/**
 * Preferred value set for Condition/Diagnosis severity grading.
 */
export const ConditionSeverity = {
  VAL24484000: ConditionSeverity_VAL24484000,
  VAL255604002: ConditionSeverity_VAL255604002,
  VAL6736007: ConditionSeverity_VAL6736007,
};
const ConditionStageType_ClinicalStagingQualifierValue: Coding = {
  code: '260998006',
  display: 'Clinical staging (qualifier value)',
  system: 'http://snomed.info/sct',
};
const ConditionStageType_PathologicalStagingQualifierValue: Coding = {
  code: '261023001',
  display: 'Pathological staging (qualifier value)',
  system: 'http://snomed.info/sct',
};
/**
 * Example value set for the type of stages of cancer and other conditions
 */
export const ConditionStageType = {
  ClinicalStagingQualifierValue: ConditionStageType_ClinicalStagingQualifierValue,
  PathologicalStagingQualifierValue: ConditionStageType_PathologicalStagingQualifierValue,
};
const condition_state_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://terminology.hl7.org/CodeSystem/condition-state',
};
const condition_state_Inactive: Coding = {
  code: 'inactive',
  display: 'Inactive',
  system: 'http://terminology.hl7.org/CodeSystem/condition-state',
};
const condition_state_Resolved: Coding = {
  code: 'resolved',
  display: 'Resolved',
  system: 'http://terminology.hl7.org/CodeSystem/condition-state',
};
/**
 * Enumeration indicating whether the condition is currently active, inactive, or has been resolved.
 */
export const ConditionState = {
  /**
   * The condition is active.
   */
  Active: condition_state_Active,
  /**
   * The condition is inactive, but not resolved.
   */
  Inactive: condition_state_Inactive,
  /**
   * The condition is resolved.
   */
  Resolved: condition_state_Resolved,
};
const condition_ver_status_Confirmed: Coding = {
  code: 'confirmed',
  display: 'Confirmed',
  system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
};
const condition_ver_status_Differential: Coding = {
  code: 'differential',
  display: 'Differential',
  system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
};
const condition_ver_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
};
const condition_ver_status_Provisional: Coding = {
  code: 'provisional',
  display: 'Provisional',
  system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
};
const condition_ver_status_Refuted: Coding = {
  code: 'refuted',
  display: 'Refuted',
  system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
};
const condition_ver_status_Unconfirmed: Coding = {
  code: 'unconfirmed',
  display: 'Unconfirmed',
  system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
};
/**
 * The verification status to support or decline the clinical status of the condition or diagnosis.
 */
export const ConditionVerStatus = {
  /**
   * There is sufficient diagnostic and/or clinical evidence to treat this as a confirmed condition.
   */
  Confirmed: condition_ver_status_Confirmed,
  /**
   * One of a set of potential (and typically mutually exclusive) diagnoses asserted to further guide the diagnostic process and preliminary treatment.
   */
  Differential: condition_ver_status_Differential,
  /**
   * The statement was entered in error and is not valid.
   */
  EnteredInError: condition_ver_status_EnteredInError,
  /**
   * This is a tentative diagnosis - still a candidate that is under consideration.
   */
  Provisional: condition_ver_status_Provisional,
  /**
   * This condition has been ruled out by diagnostic and clinical evidence.
   */
  Refuted: condition_ver_status_Refuted,
  /**
   * There is not sufficient diagnostic and/or clinical evidence to treat this as a confirmed condition.
   */
  Unconfirmed: condition_ver_status_Unconfirmed,
};
const conformance_expectation_MAY: Coding = {
  code: 'MAY',
  display: 'MAY',
  system: 'http://terminology.hl7.org/CodeSystem/conformance-expectation',
};
const conformance_expectation_SHALL: Coding = {
  code: 'SHALL',
  display: 'SHALL',
  system: 'http://terminology.hl7.org/CodeSystem/conformance-expectation',
};
const conformance_expectation_SHOULD: Coding = {
  code: 'SHOULD',
  display: 'SHOULD',
  system: 'http://terminology.hl7.org/CodeSystem/conformance-expectation',
};
const conformance_expectation_SHOULDNOT: Coding = {
  code: 'SHOULD-NOT',
  display: 'SHOULD-NOT',
  system: 'http://terminology.hl7.org/CodeSystem/conformance-expectation',
};
/**
 * Indicates the degree of adherence to a specified behavior or capability expected for a system to be deemed conformant with a specification.
 */
export const ConformanceExpectation = {
  /**
   * Support for the specified capability is not necessary to be considered conformant, and the requirement should be considered strictly optional.
   */
  MAY: conformance_expectation_MAY,
  /**
   * Support for the specified capability is required to be considered conformant.
   */
  SHALL: conformance_expectation_SHALL,
  /**
   * Support for the specified capability is strongly encouraged, and failure to support it should only occur after careful consideration.
   */
  SHOULD: conformance_expectation_SHOULD,
  /**
   * Support for the specified capability is strongly discouraged and should occur only after careful consideration.
   */
  SHOULDNOT: conformance_expectation_SHOULDNOT,
};
const consent_action_Access: Coding = {
  code: 'access',
  display: 'Access',
  system: 'http://terminology.hl7.org/CodeSystem/consentaction',
};
const consent_action_Collect: Coding = {
  code: 'collect',
  display: 'Collect',
  system: 'http://terminology.hl7.org/CodeSystem/consentaction',
};
const consent_action_AccessAndCorrect: Coding = {
  code: 'correct',
  display: 'Access and Correct',
  system: 'http://terminology.hl7.org/CodeSystem/consentaction',
};
const consent_action_Disclose: Coding = {
  code: 'disclose',
  display: 'Disclose',
  system: 'http://terminology.hl7.org/CodeSystem/consentaction',
};
const consent_action_Use: Coding = {
  code: 'use',
  display: 'Use',
  system: 'http://terminology.hl7.org/CodeSystem/consentaction',
};
/**
 * This value set includes sample Consent Action codes.
 */
export const ConsentAction = {
  /**
   * Retrieval without permitting collection, use or disclosure. e.g., no screen-scraping for collection, use or disclosure (view-only access)
   */
  Access: consent_action_Access,
  /**
   * Gather retrieved information for storage
   */
  Collect: consent_action_Collect,
  /**
   * Allow retrieval of a patient's information for the purpose of update or rectify
   */
  AccessAndCorrect: consent_action_AccessAndCorrect,
  /**
   * Transfer retrieved information
   */
  Disclose: consent_action_Disclose,
  /**
   * Utilize the retrieved information
   */
  Use: consent_action_Use,
};
const consent_category_AdvanceDirective: Coding = {
  code: 'acd',
  display: 'Advance Directive',
  system: 'http://terminology.hl7.org/CodeSystem/consentcategorycodes',
};
const consent_category_DoNotResuscitate: Coding = {
  code: 'dnr',
  display: 'Do Not Resuscitate',
  system: 'http://terminology.hl7.org/CodeSystem/consentcategorycodes',
};
const consent_category_EmergencyOnly: Coding = {
  code: 'emrgonly',
  display: 'Emergency Only',
  system: 'http://terminology.hl7.org/CodeSystem/consentcategorycodes',
};
const consent_category_HealthCareDirective: Coding = {
  code: 'hcd',
  display: 'Health Care Directive',
  system: 'http://terminology.hl7.org/CodeSystem/consentcategorycodes',
};
const consent_category_NoticeOfPrivacyPractices: Coding = {
  code: 'npp',
  display: 'Notice of Privacy Practices',
  system: 'http://terminology.hl7.org/CodeSystem/consentcategorycodes',
};
const consent_category_POLST: Coding = {
  code: 'polst',
  display: 'POLST',
  system: 'http://terminology.hl7.org/CodeSystem/consentcategorycodes',
};
const consent_category_ResearchInformationAccess: Coding = {
  code: 'research',
  display: 'Research Information Access',
  system: 'http://terminology.hl7.org/CodeSystem/consentcategorycodes',
};
const consent_category_DeIdentifiedInformationAccess: Coding = {
  code: 'rsdid',
  display: 'De-identified Information Access',
  system: 'http://terminology.hl7.org/CodeSystem/consentcategorycodes',
};
const consent_category_ReIdentifiableInformationAccess: Coding = {
  code: 'rsreid',
  display: 'Re-identifiable Information Access',
  system: 'http://terminology.hl7.org/CodeSystem/consentcategorycodes',
};
/**
 * This value set includes sample Consent Directive Type codes, including several consent directive related LOINC codes; HL7 VALUE SET: ActConsentType(2.16.840.1.113883.1.11.19897); examples of US realm consent directive legal descriptions and references to online and/or downloadable forms such as the SSA-827 Authorization to Disclose Information to the Social Security Administration; and other anticipated consent directives related to participation in a clinical trial, medical procedures, reproductive procedures; health care directive (Living Will); advance directive, do not resuscitate (DNR); Physician Orders for Life-Sustaining Treatment (POLST)
 */
export const ConsentCategory = {
  PrivacyPolicyAcknowledgmentDocument: C80DocTypecodes_PrivacyPolicyAcknowledgmentDocument,
  PrivacyPolicyOrganizationDocument: C80DocTypecodes_PrivacyPolicyOrganizationDocument,
  PatientConsent: C80DocTypecodes_PatientConsent,
  ReleaseOfInformationConsent: C80DocTypecodes_ReleaseOfInformationConsent,
  /**
   * Any instructions, written or given verbally by a patient to a health care provider in anticipation of potential need for medical treatment. [2005 Honor My Wishes]
   */
  AdvanceDirective: consent_category_AdvanceDirective,
  /**
   * A legal document, signed by both the patient and their provider, stating a desire not to have CPR initiated in case of a cardiac event. Note: This form was replaced in 2003 with the Physician Orders for Life-Sustaining Treatment [POLST].
   */
  DoNotResuscitate: consent_category_DoNotResuscitate,
  /**
   * Opt-in to disclosure of health information for emergency only consent directive. Comment: This general consent directive specifically limits disclosure of health information for purpose of emergency treatment. Additional parameters may further limit the disclosure to specific users, roles, duration, types of information, and impose uses obligations. [ActConsentDirective (2.16.840.1.113883.1.11.20425)]
   */
  EmergencyOnly: consent_category_EmergencyOnly,
  /**
   * Patient's document telling patient's health care provider what the patient wants or does not want if the patient is diagnosed as being terminally ill and in a persistent vegetative state or in a permanently unconscious condition.[2005 Honor My Wishes]
   */
  HealthCareDirective: consent_category_HealthCareDirective,
  /**
   * Acknowledgement of custodian notice of privacy practices. Usage Notes: This type of consent directive acknowledges a custodian's notice of privacy practices including its permitted collection, access, use and disclosure of health information to users and for purposes of use specified. [ActConsentDirective (2.16.840.1.113883.1.11.20425)]
   */
  NoticeOfPrivacyPractices: consent_category_NoticeOfPrivacyPractices,
  /**
   * The Physician Order for Life-Sustaining Treatment form records a person's health care wishes for end of life emergency treatment and translates them into an order by the physician. It must be reviewed and signed by both the patient and the physician, Advanced Registered Nurse Practitioner or Physician Assistant. [2005 Honor My Wishes] Comment: Opt-in Consent Directive with restrictions.
   */
  POLST: consent_category_POLST,
  /**
   * Consent to have healthcare information in an electronic health record accessed for research purposes. [VALUE SET: ActConsentType (2.16.840.1.113883.1.11.19897)]
   */
  ResearchInformationAccess: consent_category_ResearchInformationAccess,
  /**
   * Consent to have de-identified healthcare information in an electronic health record that is accessed for research purposes, but without consent to re-identify the information under any circumstance. [VALUE SET: ActConsentType (2.16.840.1.113883.1.11.19897)
   */
  DeIdentifiedInformationAccess: consent_category_DeIdentifiedInformationAccess,
  /**
   * Consent to have de-identified healthcare information in an electronic health record that is accessed for research purposes re-identified under specific circumstances outlined in the consent. [VALUE SET: ActConsentType (2.16.840.1.113883.1.11.19897)]
   */
  ReIdentifiableInformationAccess: consent_category_ReIdentifiableInformationAccess,
};
const ConsentContentClass_CDADocuments: Coding = {
  code: 'application/hl7-cda+xml',
  display: 'CDA Documents',
  system: 'urn:ietf:bcp:13',
};
const ConsentContentClass_LipidLabReport: Coding = {
  code: 'http://hl7.org/fhir/StructureDefinition/lipidprofile',
  display: 'Lipid Lab Report',
  system: 'urn:ietf:rfc:3986',
};
/**
 * This value set includes the FHIR resource types, along with some other important content class codes
 */
export const ConsentContentClass = {
  /**
   * A financial tool for tracking value accrued for a particular purpose.  In the healthcare field, used to track charges for a patient, cost centers, etc.
   */
  Account: resource_types_Account,
  /**
   * This resource allows for the definition of some activity to be performed, independent of a particular patient, practitioner, or other performance context.
   */
  ActivityDefinition: resource_types_ActivityDefinition,
  /**
   * Actual or  potential/avoided event causing unintended physical injury resulting from or contributed to by medical care, a research study or other healthcare setting factors that requires additional monitoring, treatment, or hospitalization, or that results in death.
   */
  AdverseEvent: resource_types_AdverseEvent,
  /**
   * Risk of harmful or undesirable, physiological response which is unique to an individual and associated with exposure to a substance.
   */
  AllergyIntolerance: resource_types_AllergyIntolerance,
  CDADocuments: ConsentContentClass_CDADocuments,
  /**
   * A booking of a healthcare event among patient(s), practitioner(s), related person(s) and/or device(s) for a specific date/time. This may result in one or more Encounter(s).
   */
  Appointment: resource_types_Appointment,
  /**
   * A reply to an appointment request for a patient and/or practitioner(s), such as a confirmation or rejection.
   */
  AppointmentResponse: resource_types_AppointmentResponse,
  /**
   * A record of an event made for purposes of maintaining a security log. Typical uses include detection of intrusion attempts and monitoring for inappropriate usage.
   */
  AuditEvent: resource_types_AuditEvent,
  /**
   * Basic is used for handling concepts not yet defined in FHIR, narrative-only resources that don't map to an existing resource, and custom resources not appropriate for inclusion in the FHIR specification.
   */
  Basic: resource_types_Basic,
  /**
   * A resource that represents the data of a single raw artifact as digital content accessible in its native format.  A Binary resource can contain any content, whether text, image, pdf, zip archive, etc.
   */
  Binary: resource_types_Binary,
  /**
   * A material substance originating from a biological entity intended to be transplanted or infused
   * into another (possibly the same) biological entity.
   */
  BiologicallyDerivedProduct: resource_types_BiologicallyDerivedProduct,
  /**
   * Record details about an anatomical structure.  This resource may be used when a coded concept does not provide the necessary detail needed for the use case.
   */
  BodyStructure: resource_types_BodyStructure,
  /**
   * A container for a collection of resources.
   */
  Bundle: resource_types_Bundle,
  /**
   * A Capability Statement documents a set of capabilities (behaviors) of a FHIR Server for a particular version of FHIR that may be used as a statement of actual server functionality or a statement of required or desired server implementation.
   */
  CapabilityStatement: resource_types_CapabilityStatement,
  /**
   * Describes the intention of how one or more practitioners intend to deliver care for a particular patient, group or community for a period of time, possibly limited to care for a specific condition or set of conditions.
   */
  CarePlan: resource_types_CarePlan,
  /**
   * The Care Team includes all the people and organizations who plan to participate in the coordination and delivery of care for a patient.
   */
  CareTeam: resource_types_CareTeam,
  /**
   * Catalog entries are wrappers that contextualize items included in a catalog.
   */
  CatalogEntry: resource_types_CatalogEntry,
  /**
   * The resource ChargeItem describes the provision of healthcare provider products for a certain patient, therefore referring not only to the product, but containing in addition details of the provision, like date, time, amounts and participating organizations and persons. Main Usage of the ChargeItem is to enable the billing process and internal cost allocation.
   */
  ChargeItem: resource_types_ChargeItem,
  /**
   * The ChargeItemDefinition resource provides the properties that apply to the (billing) codes necessary to calculate costs and prices. The properties may differ largely depending on type and realm, therefore this resource gives only a rough structure and requires profiling for each type of billing code system.
   */
  ChargeItemDefinition: resource_types_ChargeItemDefinition,
  /**
   * A provider issued list of professional services and products which have been provided, or are to be provided, to a patient which is sent to an insurer for reimbursement.
   */
  Claim: resource_types_Claim,
  /**
   * This resource provides the adjudication details from the processing of a Claim resource.
   */
  ClaimResponse: resource_types_ClaimResponse,
  /**
   * A record of a clinical assessment performed to determine what problem(s) may affect the patient and before planning the treatments or management strategies that are best to manage a patient's condition. Assessments are often 1:1 with a clinical consultation / encounter,  but this varies greatly depending on the clinical workflow. This resource is called "ClinicalImpression" rather than "ClinicalAssessment" to avoid confusion with the recording of assessment tools such as Apgar score.
   */
  ClinicalImpression: resource_types_ClinicalImpression,
  /**
   * The CodeSystem resource is used to declare the existence of and describe a code system or code system supplement and its key properties, and optionally define a part or all of its content.
   */
  CodeSystem: resource_types_CodeSystem,
  /**
   * An occurrence of information being transmitted; e.g. an alert that was sent to a responsible provider, a public health agency that was notified about a reportable condition.
   */
  Communication: resource_types_Communication,
  /**
   * A request to convey information; e.g. the CDS system proposes that an alert be sent to a responsible provider, the CDS system proposes that the public health agency be notified about a reportable condition.
   */
  CommunicationRequest: resource_types_CommunicationRequest,
  /**
   * A compartment definition that defines how resources are accessed on a server.
   */
  CompartmentDefinition: resource_types_CompartmentDefinition,
  /**
   * A set of healthcare-related information that is assembled together into a single logical package that provides a single coherent statement of meaning, establishes its own context and that has clinical attestation with regard to who is making the statement. A Composition defines the structure and narrative content necessary for a document. However, a Composition alone does not constitute a document. Rather, the Composition must be the first entry in a Bundle where Bundle.type=document, and any other resources referenced from Composition must be included as subsequent entries in the Bundle (for example Patient, Practitioner, Encounter, etc.).
   */
  Composition: resource_types_Composition,
  /**
   * A statement of relationships from one set of concepts to one or more other concepts - either concepts in code systems, or data element/data element concepts, or classes in class models.
   */
  ConceptMap: resource_types_ConceptMap,
  /**
   * A clinical condition, problem, diagnosis, or other event, situation, issue, or clinical concept that has risen to a level of concern.
   */
  Condition: resource_types_Condition,
  /**
   * A record of a healthcare consumer’s  choices, which permits or denies identified recipient(s) or recipient role(s) to perform one or more actions within a given policy context, for specific purposes and periods of time.
   */
  Consent: resource_types_Consent,
  /**
   * Legally enforceable, formally recorded unilateral or bilateral directive i.e., a policy or agreement.
   */
  Contract: resource_types_Contract,
  /**
   * Financial instrument which may be used to reimburse or pay for health care products and services. Includes both insurance and self-payment.
   */
  Coverage: resource_types_Coverage,
  /**
   * The CoverageEligibilityRequest provides patient and insurance coverage information to an insurer for them to respond, in the form of an CoverageEligibilityResponse, with information regarding whether the stated coverage is valid and in-force and optionally to provide the insurance details of the policy.
   */
  CoverageEligibilityRequest: resource_types_CoverageEligibilityRequest,
  /**
   * This resource provides eligibility and plan details from the processing of an CoverageEligibilityRequest resource.
   */
  CoverageEligibilityResponse: resource_types_CoverageEligibilityResponse,
  /**
   * Indicates an actual or potential clinical issue with or between one or more active or proposed clinical actions for a patient; e.g. Drug-drug interaction, Ineffective treatment frequency, Procedure-condition conflict, etc.
   */
  DetectedIssue: resource_types_DetectedIssue,
  /**
   * A type of a manufactured item that is used in the provision of healthcare without being substantially changed through that activity. The device may be a medical or non-medical device.
   */
  Device: resource_types_Device,
  /**
   * The characteristics, operational status and capabilities of a medical-related component of a medical device.
   */
  DeviceDefinition: resource_types_DeviceDefinition,
  /**
   * Describes a measurement, calculation or setting capability of a medical device.
   */
  DeviceMetric: resource_types_DeviceMetric,
  /**
   * Represents a request for a patient to employ a medical device. The device may be an implantable device, or an external assistive device, such as a walker.
   */
  DeviceRequest: resource_types_DeviceRequest,
  /**
   * A record of a device being used by a patient where the record is the result of a report from the patient or another clinician.
   */
  DeviceUseStatement: resource_types_DeviceUseStatement,
  /**
   * The findings and interpretation of diagnostic  tests performed on patients, groups of patients, devices, and locations, and/or specimens derived from these. The report includes clinical context such as requesting and provider information, and some mix of atomic results, images, textual and coded interpretations, and formatted representation of diagnostic reports.
   */
  DiagnosticReport: resource_types_DiagnosticReport,
  /**
   * A collection of documents compiled for a purpose together with metadata that applies to the collection.
   */
  DocumentManifest: resource_types_DocumentManifest,
  /**
   * A reference to a document of any kind for any purpose. Provides metadata about the document so that the document can be discovered and managed. The scope of a document is any seralized object with a mime-type, so includes formal patient centric documents (CDA), cliical notes, scanned paper, and non-patient specific documents like policy text.
   */
  DocumentReference: resource_types_DocumentReference,
  /**
   * A resource that includes narrative, extensions, and contained resources.
   */
  DomainResource: resource_types_DomainResource,
  /**
   * The EffectEvidenceSynthesis resource describes the difference in an outcome between exposures states in a population where the effect estimate is derived from a combination of research studies.
   */
  EffectEvidenceSynthesis: resource_types_EffectEvidenceSynthesis,
  /**
   * An interaction between a patient and healthcare provider(s) for the purpose of providing healthcare service(s) or assessing the health status of a patient.
   */
  Encounter: resource_types_Encounter,
  /**
   * The technical details of an endpoint that can be used for electronic services, such as for web services providing XDS.b or a REST endpoint for another FHIR server. This may include any security context information.
   */
  Endpoint: resource_types_Endpoint,
  /**
   * This resource provides the insurance enrollment details to the insurer regarding a specified coverage.
   */
  EnrollmentRequest: resource_types_EnrollmentRequest,
  /**
   * This resource provides enrollment and plan details from the processing of an EnrollmentRequest resource.
   */
  EnrollmentResponse: resource_types_EnrollmentResponse,
  /**
   * An association between a patient and an organization / healthcare provider(s) during which time encounters may occur. The managing organization assumes a level of responsibility for the patient during this time.
   */
  EpisodeOfCare: resource_types_EpisodeOfCare,
  /**
   * The EventDefinition resource provides a reusable description of when a particular event can occur.
   */
  EventDefinition: resource_types_EventDefinition,
  /**
   * The Evidence resource describes the conditional state (population and any exposures being compared within the population) and outcome (if specified) that the knowledge (evidence, assertion, recommendation) is about.
   */
  Evidence: resource_types_Evidence,
  /**
   * The EvidenceVariable resource describes a "PICO" element that knowledge (evidence, assertion, recommendation) is about.
   */
  EvidenceVariable: resource_types_EvidenceVariable,
  /**
   * Example of workflow instance.
   */
  ExampleScenario: resource_types_ExampleScenario,
  /**
   * This resource provides: the claim details; adjudication details from the processing of a Claim; and optionally account balance information, for informing the subscriber of the benefits provided.
   */
  ExplanationOfBenefit: resource_types_ExplanationOfBenefit,
  /**
   * Significant health conditions for a person related to the patient relevant in the context of care for the patient.
   */
  FamilyMemberHistory: resource_types_FamilyMemberHistory,
  /**
   * Prospective warnings of potential issues when providing care to the patient.
   */
  Flag: resource_types_Flag,
  /**
   * Describes the intended objective(s) for a patient, group or organization care, for example, weight loss, restoring an activity of daily living, obtaining herd immunity via immunization, meeting a process improvement objective, etc.
   */
  Goal: resource_types_Goal,
  /**
   * A formal computable definition of a graph of resources - that is, a coherent set of resources that form a graph by following references. The Graph Definition resource defines a set and makes rules about the set.
   */
  GraphDefinition: resource_types_GraphDefinition,
  /**
   * Represents a defined collection of entities that may be discussed or acted upon collectively but which are not expected to act collectively, and are not formally or legally recognized; i.e. a collection of entities that isn't an Organization.
   */
  Group: resource_types_Group,
  /**
   * A guidance response is the formal response to a guidance request, including any output parameters returned by the evaluation, as well as the description of any proposed actions to be taken.
   */
  GuidanceResponse: resource_types_GuidanceResponse,
  /**
   * The details of a healthcare service available at a location.
   */
  HealthcareService: resource_types_HealthcareService,
  LipidLabReport: ConsentContentClass_LipidLabReport,
  /**
   * Representation of the content produced in a DICOM imaging study. A study comprises a set of series, each of which includes a set of Service-Object Pair Instances (SOP Instances - images or other data) acquired or produced in a common context.  A series is of only one modality (e.g. X-ray, CT, MR, ultrasound), but a study may have multiple series of different modalities.
   */
  ImagingStudy: resource_types_ImagingStudy,
  /**
   * Describes the event of a patient being administered a vaccine or a record of an immunization as reported by a patient, a clinician or another party.
   */
  Immunization: resource_types_Immunization,
  /**
   * Describes a comparison of an immunization event against published recommendations to determine if the administration is "valid" in relation to those  recommendations.
   */
  ImmunizationEvaluation: resource_types_ImmunizationEvaluation,
  /**
   * A patient's point-in-time set of recommendations (i.e. forecasting) according to a published schedule with optional supporting justification.
   */
  ImmunizationRecommendation: resource_types_ImmunizationRecommendation,
  /**
   * A set of rules of how a particular interoperability or standards problem is solved - typically through the use of FHIR resources. This resource is used to gather all the parts of an implementation guide into a logical whole and to publish a computable definition of all the parts.
   */
  ImplementationGuide: resource_types_ImplementationGuide,
  /**
   * Details of a Health Insurance product/plan provided by an organization.
   */
  InsurancePlan: resource_types_InsurancePlan,
  /**
   * Invoice containing collected ChargeItems from an Account with calculated individual and total price for Billing purpose.
   */
  Invoice: resource_types_Invoice,
  /**
   * The Library resource is a general-purpose container for knowledge asset definitions. It can be used to describe and expose existing knowledge assets such as logic libraries and information model descriptions, as well as to describe a collection of knowledge assets.
   */
  Library: resource_types_Library,
  /**
   * Identifies two or more records (resource instances) that refer to the same real-world "occurrence".
   */
  Linkage: resource_types_Linkage,
  /**
   * A list is a curated collection of resources.
   */
  List: resource_types_List,
  /**
   * Details and position information for a physical place where services are provided and resources and participants may be stored, found, contained, or accommodated.
   */
  Location: resource_types_Location,
  /**
   * The Measure resource provides the definition of a quality measure.
   */
  Measure: resource_types_Measure,
  /**
   * The MeasureReport resource contains the results of the calculation of a measure; and optionally a reference to the resources involved in that calculation.
   */
  MeasureReport: resource_types_MeasureReport,
  /**
   * A photo, video, or audio recording acquired or used in healthcare. The actual content may be inline or provided by direct reference.
   */
  Media: resource_types_Media,
  /**
   * This resource is primarily used for the identification and definition of a medication for the purposes of prescribing, dispensing, and administering a medication as well as for making statements about medication use.
   */
  Medication: resource_types_Medication,
  /**
   * Describes the event of a patient consuming or otherwise being administered a medication.  This may be as simple as swallowing a tablet or it may be a long running infusion.  Related resources tie this event to the authorizing prescription, and the specific encounter between patient and health care practitioner.
   */
  MedicationAdministration: resource_types_MedicationAdministration,
  /**
   * Indicates that a medication product is to be or has been dispensed for a named person/patient.  This includes a description of the medication product (supply) provided and the instructions for administering the medication.  The medication dispense is the result of a pharmacy system responding to a medication order.
   */
  MedicationDispense: resource_types_MedicationDispense,
  /**
   * Information about a medication that is used to support knowledge.
   */
  MedicationKnowledge: resource_types_MedicationKnowledge,
  /**
   * An order or request for both supply of the medication and the instructions for administration of the medication to a patient. The resource is called "MedicationRequest" rather than "MedicationPrescription" or "MedicationOrder" to generalize the use across inpatient and outpatient settings, including care plans, etc., and to harmonize with workflow patterns.
   */
  MedicationRequest: resource_types_MedicationRequest,
  /**
   * A record of a medication that is being consumed by a patient.   A MedicationStatement may indicate that the patient may be taking the medication now or has taken the medication in the past or will be taking the medication in the future.  The source of this information can be the patient, significant other (such as a family member or spouse), or a clinician.  A common scenario where this information is captured is during the history taking process during a patient visit or stay.   The medication information may come from sources such as the patient's memory, from a prescription bottle,  or from a list of medications the patient, clinician or other party maintains.
   * The primary difference between a medication statement and a medication administration is that the medication administration has complete administration information and is based on actual administration information from the person who administered the medication.  A medication statement is often, if not always, less specific.  There is no required date/time when the medication was administered, in fact we only know that a source has reported the patient is taking this medication, where details such as time, quantity, or rate or even medication product may be incomplete or missing or less precise.  As stated earlier, the medication statement information may come from the patient's memory, from a prescription bottle or from a list of medications the patient, clinician or other party maintains.  Medication administration is more formal and is not missing detailed information.
   */
  MedicationStatement: resource_types_MedicationStatement,
  /**
   * Detailed definition of a medicinal product, typically for uses other than direct patient care (e.g. regulatory use).
   */
  MedicinalProduct: resource_types_MedicinalProduct,
  /**
   * The regulatory authorization of a medicinal product.
   */
  MedicinalProductAuthorization: resource_types_MedicinalProductAuthorization,
  /**
   * The clinical particulars - indications, contraindications etc. of a medicinal product, including for regulatory purposes.
   */
  MedicinalProductContraindication: resource_types_MedicinalProductContraindication,
  /**
   * Indication for the Medicinal Product.
   */
  MedicinalProductIndication: resource_types_MedicinalProductIndication,
  /**
   * An ingredient of a manufactured item or pharmaceutical product.
   */
  MedicinalProductIngredient: resource_types_MedicinalProductIngredient,
  /**
   * The interactions of the medicinal product with other medicinal products, or other forms of interactions.
   */
  MedicinalProductInteraction: resource_types_MedicinalProductInteraction,
  /**
   * The manufactured item as contained in the packaged medicinal product.
   */
  MedicinalProductManufactured: resource_types_MedicinalProductManufactured,
  /**
   * A medicinal product in a container or package.
   */
  MedicinalProductPackaged: resource_types_MedicinalProductPackaged,
  /**
   * A pharmaceutical product described in terms of its composition and dose form.
   */
  MedicinalProductPharmaceutical: resource_types_MedicinalProductPharmaceutical,
  /**
   * Describe the undesirable effects of the medicinal product.
   */
  MedicinalProductUndesirableEffect: resource_types_MedicinalProductUndesirableEffect,
  /**
   * Defines the characteristics of a message that can be shared between systems, including the type of event that initiates the message, the content to be transmitted and what response(s), if any, are permitted.
   */
  MessageDefinition: resource_types_MessageDefinition,
  /**
   * The header for a message exchange that is either requesting or responding to an action.  The reference(s) that are the subject of the action as well as other information related to the action are typically transmitted in a bundle in which the MessageHeader resource instance is the first resource in the bundle.
   */
  MessageHeader: resource_types_MessageHeader,
  /**
   * Raw data describing a biological sequence.
   */
  MolecularSequence: resource_types_MolecularSequence,
  /**
   * A curated namespace that issues unique symbols within that namespace for the identification of concepts, people, devices, etc.  Represents a "System" used within the Identifier and Coding data types.
   */
  NamingSystem: resource_types_NamingSystem,
  /**
   * A request to supply a diet, formula feeding (enteral) or oral nutritional supplement to a patient/resident.
   */
  NutritionOrder: resource_types_NutritionOrder,
  /**
   * Measurements and simple assertions made about a patient, device or other subject.
   */
  Observation: resource_types_Observation,
  /**
   * Set of definitional characteristics for a kind of observation or measurement produced or consumed by an orderable health care service.
   */
  ObservationDefinition: resource_types_ObservationDefinition,
  /**
   * A formal computable definition of an operation (on the RESTful interface) or a named query (using the search interaction).
   */
  OperationDefinition: resource_types_OperationDefinition,
  /**
   * A collection of error, warning, or information messages that result from a system action.
   */
  OperationOutcome: resource_types_OperationOutcome,
  /**
   * A formally or informally recognized grouping of people or organizations formed for the purpose of achieving some form of collective action.  Includes companies, institutions, corporations, departments, community groups, healthcare practice groups, payer/insurer, etc.
   */
  Organization: resource_types_Organization,
  /**
   * Defines an affiliation/assotiation/relationship between 2 distinct oganizations, that is not a part-of relationship/sub-division relationship.
   */
  OrganizationAffiliation: resource_types_OrganizationAffiliation,
  /**
   * This resource is a non-persisted resource used to pass information into and back from an [operation](operations.html). It has no other use, and there is no RESTful endpoint associated with it.
   */
  Parameters: resource_types_Parameters,
  /**
   * Demographics and other administrative information about an individual or animal receiving care or other health-related services.
   */
  Patient: resource_types_Patient,
  /**
   * This resource provides the status of the payment for goods and services rendered, and the request and response resource references.
   */
  PaymentNotice: resource_types_PaymentNotice,
  /**
   * This resource provides the details including amount of a payment and allocates the payment items being paid.
   */
  PaymentReconciliation: resource_types_PaymentReconciliation,
  /**
   * Demographics and administrative information about a person independent of a specific health-related context.
   */
  Person: resource_types_Person,
  /**
   * This resource allows for the definition of various types of plans as a sharable, consumable, and executable artifact. The resource is general enough to support the description of a broad range of clinical artifacts such as clinical decision support rules, order sets and protocols.
   */
  PlanDefinition: resource_types_PlanDefinition,
  /**
   * A person who is directly or indirectly involved in the provisioning of healthcare.
   */
  Practitioner: resource_types_Practitioner,
  /**
   * A specific set of Roles/Locations/specialties/services that a practitioner may perform at an organization for a period of time.
   */
  PractitionerRole: resource_types_PractitionerRole,
  /**
   * An action that is or was performed on or for a patient. This can be a physical intervention like an operation, or less invasive like long term services, counseling, or hypnotherapy.
   */
  Procedure: resource_types_Procedure,
  /**
   * Provenance of a resource is a record that describes entities and processes involved in producing and delivering or otherwise influencing that resource. Provenance provides a critical foundation for assessing authenticity, enabling trust, and allowing reproducibility. Provenance assertions are a form of contextual metadata and can themselves become important records with their own provenance. Provenance statement indicates clinical significance in terms of confidence in authenticity, reliability, and trustworthiness, integrity, and stage in lifecycle (e.g. Document Completion - has the artifact been legally authenticated), all of which may impact security, privacy, and trust policies.
   */
  Provenance: resource_types_Provenance,
  /**
   * A structured set of questions intended to guide the collection of answers from end-users. Questionnaires provide detailed control over order, presentation, phraseology and grouping to allow coherent, consistent data collection.
   */
  Questionnaire: resource_types_Questionnaire,
  /**
   * A structured set of questions and their answers. The questions are ordered and grouped into coherent subsets, corresponding to the structure of the grouping of the questionnaire being responded to.
   */
  QuestionnaireResponse: resource_types_QuestionnaireResponse,
  /**
   * Information about a person that is involved in the care for a patient, but who is not the target of healthcare, nor has a formal responsibility in the care process.
   */
  RelatedPerson: resource_types_RelatedPerson,
  /**
   * A group of related requests that can be used to capture intended activities that have inter-dependencies such as "give this medication after that one".
   */
  RequestGroup: resource_types_RequestGroup,
  /**
   * The ResearchDefinition resource describes the conditional state (population and any exposures being compared within the population) and outcome (if specified) that the knowledge (evidence, assertion, recommendation) is about.
   */
  ResearchDefinition: resource_types_ResearchDefinition,
  /**
   * The ResearchElementDefinition resource describes a "PICO" element that knowledge (evidence, assertion, recommendation) is about.
   */
  ResearchElementDefinition: resource_types_ResearchElementDefinition,
  /**
   * A process where a researcher or organization plans and then executes a series of steps intended to increase the field of healthcare-related knowledge.  This includes studies of safety, efficacy, comparative effectiveness and other information about medications, devices, therapies and other interventional and investigative techniques.  A ResearchStudy involves the gathering of information about human or animal subjects.
   */
  ResearchStudy: resource_types_ResearchStudy,
  /**
   * A physical entity which is the primary unit of operational and/or administrative interest in a study.
   */
  ResearchSubject: resource_types_ResearchSubject,
  /**
   * This is the base resource type for everything.
   */
  Resource: resource_types_Resource,
  /**
   * An assessment of the likely outcome(s) for a patient or other subject as well as the likelihood of each outcome.
   */
  RiskAssessment: resource_types_RiskAssessment,
  /**
   * The RiskEvidenceSynthesis resource describes the likelihood of an outcome in a population plus exposure state where the risk estimate is derived from a combination of research studies.
   */
  RiskEvidenceSynthesis: resource_types_RiskEvidenceSynthesis,
  /**
   * A container for slots of time that may be available for booking appointments.
   */
  Schedule: resource_types_Schedule,
  /**
   * A search parameter that defines a named search item that can be used to search/filter on a resource.
   */
  SearchParameter: resource_types_SearchParameter,
  /**
   * A record of a request for service such as diagnostic investigations, treatments, or operations to be performed.
   */
  ServiceRequest: resource_types_ServiceRequest,
  /**
   * A slot of time on a schedule that may be available for booking appointments.
   */
  Slot: resource_types_Slot,
  /**
   * A sample to be used for analysis.
   */
  Specimen: resource_types_Specimen,
  /**
   * A kind of specimen with associated set of requirements.
   */
  SpecimenDefinition: resource_types_SpecimenDefinition,
  /**
   * A definition of a FHIR structure. This resource is used to describe the underlying resources, data types defined in FHIR, and also for describing extensions and constraints on resources and data types.
   */
  StructureDefinition: resource_types_StructureDefinition,
  /**
   * A Map of relationships between 2 structures that can be used to transform data.
   */
  StructureMap: resource_types_StructureMap,
  /**
   * The subscription resource is used to define a push-based subscription from a server to another system. Once a subscription is registered with the server, the server checks every resource that is created or updated, and if the resource matches the given criteria, it sends a message on the defined "channel" so that another system can take an appropriate action.
   */
  Subscription: resource_types_Subscription,
  /**
   * A homogeneous material with a definite composition.
   */
  Substance: resource_types_Substance,
  /**
   * Nucleic acids are defined by three distinct elements: the base, sugar and linkage. Individual substance/moiety IDs will be created for each of these elements. The nucleotide sequence will be always entered in the 5’-3’ direction.
   */
  SubstanceNucleicAcid: resource_types_SubstanceNucleicAcid,
  /**
   * Todo.
   */
  SubstancePolymer: resource_types_SubstancePolymer,
  /**
   * A SubstanceProtein is defined as a single unit of a linear amino acid sequence, or a combination of subunits that are either covalently linked or have a defined invariant stoichiometric relationship. This includes all synthetic, recombinant and purified SubstanceProteins of defined sequence, whether the use is therapeutic or prophylactic. This set of elements will be used to describe albumins, coagulation factors, cytokines, growth factors, peptide/SubstanceProtein hormones, enzymes, toxins, toxoids, recombinant vaccines, and immunomodulators.
   */
  SubstanceProtein: resource_types_SubstanceProtein,
  /**
   * Todo.
   */
  SubstanceReferenceInformation: resource_types_SubstanceReferenceInformation,
  /**
   * Source material shall capture information on the taxonomic and anatomical origins as well as the fraction of a material that can result in or can be modified to form a substance. This set of data elements shall be used to define polymer substances isolated from biological matrices. Taxonomic and anatomical origins shall be described using a controlled vocabulary as required. This information is captured for naturally derived polymers ( . starch) and structurally diverse substances. For Organisms belonging to the Kingdom Plantae the Substance level defines the fresh material of a single species or infraspecies, the Herbal Drug and the Herbal preparation. For Herbal preparations, the fraction information will be captured at the Substance information level and additional information for herbal extracts will be captured at the Specified Substance Group 1 information level. See for further explanation the Substance Class: Structurally Diverse and the herbal annex.
   */
  SubstanceSourceMaterial: resource_types_SubstanceSourceMaterial,
  /**
   * The detailed description of a substance, typically at a level beyond what is used for prescribing.
   */
  SubstanceSpecification: resource_types_SubstanceSpecification,
  /**
   * Record of delivery of what is supplied.
   */
  SupplyDelivery: resource_types_SupplyDelivery,
  /**
   * A record of a request for a medication, substance or device used in the healthcare setting.
   */
  SupplyRequest: resource_types_SupplyRequest,
  /**
   * A task to be performed.
   */
  Task: resource_types_Task,
  /**
   * A TerminologyCapabilities resource documents a set of capabilities (behaviors) of a FHIR Terminology Server that may be used as a statement of actual server functionality or a statement of required or desired server implementation.
   */
  TerminologyCapabilities: resource_types_TerminologyCapabilities,
  /**
   * A summary of information based on the results of executing a TestScript.
   */
  TestReport: resource_types_TestReport,
  /**
   * A structured set of tests against a FHIR server or client implementation to determine compliance against the FHIR specification.
   */
  TestScript: resource_types_TestScript,
  /**
   * A ValueSet resource instance specifies a set of codes drawn from one or more code systems, intended for use in a particular context. Value sets link between [[[CodeSystem]]] definitions and their use in [coded elements](terminologies.html).
   */
  ValueSet: resource_types_ValueSet,
  /**
   * Describes validation requirements, source(s), status and dates for one or more elements.
   */
  VerificationResult: resource_types_VerificationResult,
  /**
   * An authorization for the provision of glasses and/or contact lenses to a patient.
   */
  VisionPrescription: resource_types_VisionPrescription,
};
const consent_data_meaning_AuthoredBy: Coding = {
  code: 'authoredby',
  display: 'AuthoredBy',
  system: 'http://hl7.org/fhir/consent-data-meaning',
};
const consent_data_meaning_Dependents: Coding = {
  code: 'dependents',
  display: 'Dependents',
  system: 'http://hl7.org/fhir/consent-data-meaning',
};
const consent_data_meaning_Instance: Coding = {
  code: 'instance',
  display: 'Instance',
  system: 'http://hl7.org/fhir/consent-data-meaning',
};
const consent_data_meaning_Related: Coding = {
  code: 'related',
  display: 'Related',
  system: 'http://hl7.org/fhir/consent-data-meaning',
};
/**
 * How a resource reference is interpreted when testing consent restrictions.
 */
export const ConsentDataMeaning = {
  /**
   * The consent applies to instances of resources that are authored by.
   */
  AuthoredBy: consent_data_meaning_AuthoredBy,
  /**
   * The consent applies directly to the instance of the resource and instances that refer to it.
   */
  Dependents: consent_data_meaning_Dependents,
  /**
   * The consent applies directly to the instance of the resource.
   */
  Instance: consent_data_meaning_Instance,
  /**
   * The consent applies directly to the instance of the resource and instances it refers to.
   */
  Related: consent_data_meaning_Related,
};
const consent_performer_Consenter: Coding = {
  code: 'consenter',
  display: 'Consenter',
  system: 'http://hl7.org/fhir/consentperformer',
};
const consent_performer_Delegatee: Coding = {
  code: 'delegatee',
  display: 'Delegatee',
  system: 'http://hl7.org/fhir/consentperformer',
};
const consent_performer_Delegator: Coding = {
  code: 'delegator',
  display: 'Delegator',
  system: 'http://hl7.org/fhir/consentperformer',
};
const consent_performer_Grantee: Coding = {
  code: 'grantee',
  display: 'Grantee',
  system: 'http://hl7.org/fhir/consentperformer',
};
const consent_performer_Grantor: Coding = {
  code: 'grantor',
  display: 'Grantor',
  system: 'http://hl7.org/fhir/consentperformer',
};
/**
 * This value set includes sample Performer Role codes.
 */
export const ConsentPerformer = {
  /**
   * An entity or an entity's delegatee who is the grantee in an agreement such as a consent for services, advanced directive, or a privacy consent directive in accordance with jurisdictional, organizational, or patient policy.
   */
  Consenter: consent_performer_Consenter,
  /**
   * A party to whom some right or authority is granted by a delegator.
   */
  Delegatee: consent_performer_Delegatee,
  /**
   * A party that grants all or some portion its right or authority to another party.
   */
  Delegator: consent_performer_Delegator,
  /**
   * An entity which accepts certain rights or authority from a grantor.
   */
  Grantee: consent_performer_Grantee,
  /**
   * An entity which agrees to confer certain rights or authority to a grantee.
   */
  Grantor: consent_performer_Grantor,
};
const consent_policy_ATELGAOptInConsent: Coding = {
  code: 'at-elga',
  display: 'AT ELGA Opt-in Consent',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_CommonRuleInformedConsent: Coding = {
  code: 'cric',
  display: 'Common Rule Informed Consent',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_MichiganBehaviorAndMentalHealthConsent: Coding = {
  code: 'dch-3927',
  display: 'Michigan behavior and mental health consent',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_PopulationOriginsAndAncestryResearchConsent: Coding = {
  code: 'ga4gh',
  display: 'Population origins and ancestry research consent',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_HIPAAAuthorization: Coding = {
  code: 'hipaa-auth',
  display: 'HIPAA Authorization',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_HIPAANoticeOfPrivacyPractices: Coding = {
  code: 'hipaa-npp',
  display: 'HIPAA Notice of Privacy Practices',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_HIPAAResearchAuthorization: Coding = {
  code: 'hipaa-research',
  display: 'HIPAA Research Authorization',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_HIPAARestrictions: Coding = {
  code: 'hipaa-restrictions',
  display: 'HIPAA Restrictions',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_HIPAASelfPayRestriction: Coding = {
  code: 'hipaa-self-pay',
  display: 'HIPAA Self-Pay Restriction',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_IllinoisConsentByMinorsToMedicalProcedures: Coding = {
  code: 'illinois-minor-procedure',
  display: 'Illinois Consent by Minors to Medical Procedures',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_MichiganMDHHS5515ConsentToShareBehavioralHealthInformationForCareCoordinationPurposes: Coding = {
  code: 'mdhhs-5515',
  display: 'Michigan MDHHS-5515 Consent to Share Behavioral Health Information for Care Coordination Purposes',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_NCICancerClinicalTrialConsent: Coding = {
  code: 'nci',
  display: 'NCI Cancer Clinical Trial consent',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_NIHAuthorizationForTheReleaseOfMedicalInformation: Coding = {
  code: 'nih-527',
  display: 'NIH Authorization for the Release of Medical Information',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_NIHGlobalRareDiseasePatientRegistryAndDataRepositoryConsent: Coding = {
  code: 'nih-grdr',
  display: 'NIH Global Rare Disease Patient Registry and Data Repository consent',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_HHSNIHHIPAAResearchAuthorization: Coding = {
  code: 'nih-hipaa',
  display: 'HHS NIH HIPAA Research Authorization',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_NLLSPPermission: Coding = {
  code: 'nl-lsp',
  display: 'NL LSP Permission',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_NewYorkStateSurgicalAndInvasiveProcedureProtocol: Coding = {
  code: 'nyssipp',
  display: 'New York State Surgical and Invasive Procedure Protocol',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_SquaxinIndianBehavioralHealthAndHIPAAConsent: Coding = {
  code: 'squaxin',
  display: 'Squaxin Indian behavioral health and HIPAA consent',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_SSAAuthorizationToDisclose: Coding = {
  code: 'ssa-827',
  display: 'SSA Authorization to Disclose',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_VAForm100484: Coding = {
  code: 'va-10-0484',
  display: 'VA Form 10-0484',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_VAForm100485: Coding = {
  code: 'va-10-0485',
  display: 'VA Form 10-0485',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_VAForm101010116: Coding = {
  code: 'va-10-10116',
  display: 'VA Form 10-10-10116',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_VAForm105345: Coding = {
  code: 'va-10-5345',
  display: 'VA Form 10-5345',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_VAForm105345a: Coding = {
  code: 'va-10-5345a',
  display: 'VA Form 10-5345a',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_VAForm105345aMHV: Coding = {
  code: 'va-10-5345a-mhv',
  display: 'VA Form 10-5345a-MHV',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
const consent_policy_VAForm214142: Coding = {
  code: 'va-21-4142',
  display: 'VA Form 21-4142',
  system: 'http://terminology.hl7.org/CodeSystem/consentpolicycodes',
};
/**
 * This value set includes sample Regulatory consent policy types from the US and other regions.
 */
export const ConsentPolicy = {
  /**
   * Pursuant to Sec. 2 no. 9 Health Telematics Act 2012, ELGA Health Data ( “ELGA-Gesundheitsdaten”) = Medical documents. Austria opted for an opt-out approach. This means that a person is by default ‘ELGA participant’ unless he/she objects. ELGA participants have the following options: General opt out: No participation in ELGA, Partial opt-out: No participation in a particular ELGA application, e.g. eMedication and Case-specific opt-out: No participation in ELGA only regarding a particular case/treatment. There is the possibility to opt-in again. ELGA participants can also exclude the access of a particular ELGA healthcare provider to a particular piece of or all of their ELGA data. http://ec.europa.eu/health/ehealth/docs/laws_austria_en.pdf
   */
  ATELGAOptInConsent: consent_policy_ATELGAOptInConsent,
  /**
   * 45 CFR part 46 §46.116 General requirements for informed consent; and §46.117 Documentation of informed consent. https://www.gpo.gov/fdsys/pkg/FR-2017-01-19/pdf/2017-01058.pdf
   */
  CommonRuleInformedConsent: consent_policy_CommonRuleInformedConsent,
  /**
   * Michigan DCH-3927 Consent to Share Behavioral Health Information for Care Coordination Purposes, which combines 42 CFR Part 2 and Michigan Mental Health Code, Act 258 of 1974. Form is available at http://www.michigan.gov/documents/mdch/DCH-3927_Consent_to_Share_Health_Information_477005_7.docx
   */
  MichiganBehaviorAndMentalHealthConsent: consent_policy_MichiganBehaviorAndMentalHealthConsent,
  /**
   * Global Alliance for Genomic Health Data Sharing Consent Form is an example of the GA4GH Population origins and ancestry research consent form. Consenters agree to permitting a specified research project to collect ancestry and genetic information in controlled-access databases, and to allow other researchers to use deidentified information from those databases. http://www.commonaccord.org/index.php?action=doc&file=Wx/org/genomicsandhealth/REWG/Demo/Roberta_Robinson_US
   */
  PopulationOriginsAndAncestryResearchConsent: consent_policy_PopulationOriginsAndAncestryResearchConsent,
  /**
   * HIPAA 45 CFR Section 164.508 Uses and disclosures for which an authorization is required. (a) Standard: Authorizations for uses and disclosures. (1) Authorization required: General rule. Except as otherwise permitted or required by this subchapter, a covered entity SHALL not use or disclose protected health information without an authorization that is valid under this section. When a covered entity obtains or receives a valid authorization for its use or disclosure of protected health information, such use or disclosure must be consistent with such authorization. Usage Note: Authorizations governed under this regulation meet the definition of an opt in class of consent directive.
   */
  HIPAAAuthorization: consent_policy_HIPAAAuthorization,
  /**
   * 164.520  Notice of privacy practices for protected health information. (1) Right to notice. Except as provided by paragraph (a)(2) or (3) of this section, an individual has a right to adequate notice of the uses and disclosures of protected health information that may be made by the covered entity, and of the individual's rights and the covered entity's legal duties with respect to protected health information. Usage Note: Restrictions governed under this regulation meet the definition of an implied with an opportunity to dissent class of consent directive.
   */
  HIPAANoticeOfPrivacyPractices: consent_policy_HIPAANoticeOfPrivacyPractices,
  /**
   * HIPAA 45 CFR 164.508 - Uses and disclosures for which an authorization is required. (a) Standard: Authorizations for uses and disclosures. (3) Compound authorizations. An authorization for use or disclosure of protected health information SHALL NOT be combined with any other document to create a compound authorization, except as follows: (i) An authorization for the use or disclosure of protected health information for a research study may be combined with any other type of written permission for the same or another research study. This exception includes combining an authorization for the use or disclosure of protected health information for a research study with another authorization for the same research study, with an authorization for the creation or maintenance of a research database or repository, or with a consent to participate in research. Where a covered health care provider has conditioned the provision of research-related treatment on the provision of one of the authorizations, as permitted under paragraph (b)(4)(i) of this section, any compound authorization created under this paragraph must clearly differentiate between the conditioned and unconditioned components and provide the individual with an opportunity to opt in to the research activities described in the unconditioned authorization. Usage Notes: See HHS http://www.hhs.gov/hipaa/for-professionals/special-topics/research/index.html and OCR http://www.hhs.gov/hipaa/for-professionals/special-topics/research/index.html
   */
  HIPAAResearchAuthorization: consent_policy_HIPAAResearchAuthorization,
  /**
   * HIPAA 45 CFR 164.510 - Uses and disclosures requiring an opportunity for the individual to agree or to object. A covered entity may use or disclose protected health information, provided that the individual is informed in advance of the use or disclosure and has the opportunity to agree to or prohibit or restrict the use or disclosure, in accordance with the applicable requirements of this section. The covered entity may orally inform the individual of and obtain the individual's oral agreement or objection to a use or disclosure permitted by this section. Usage Note: Restrictions governed under this regulation meet the definition of an opt out with exception class of consent directive.
   */
  HIPAARestrictions: consent_policy_HIPAARestrictions,
  /**
   * HIPAA 45 CFR 164.522(a) Right To Request a Restriction of Uses and Disclosures. (vi) A covered entity must agree to the request of an individual to restrict disclosure of protected health information about the individual to a health plan if: (A) The disclosure is for the purpose of carrying out payment or health care operations and is not otherwise required by law; and (B) The protected health information pertains solely to a health care item or service for which the individual, or person other than the health plan on behalf of the individual, has paid the covered entity in full. Usage Note: Restrictions governed under this regulation meet the definition of an opt out with exception class of consent directive. Opt out is limited to disclosures to a payer for payment and operations purpose of use. See HL7 HIPAA Self-Pay code in ActPrivacyLaw (2.16.840.1.113883.1.11.20426).
   */
  HIPAASelfPayRestriction: consent_policy_HIPAASelfPayRestriction,
  /**
   * The consent to the performance of a medical or surgical procedure by a physician licensed to practice medicine and surgery, a licensed advanced practice nurse, or a licensed physician assistant executed by a married person who is a minor, by a parent who is a minor, by a pregnant woman who is a minor, or by any person 18 years of age or older, is not voidable because of such minority, and, for such purpose, a married person who is a minor, a parent who is a minor, a pregnant woman who is a minor, or any person 18 years of age or older, is deemed to have the same legal capacity to act and has the same powers and obligations as has a person of legal age. Consent by Minors to Medical Procedures Act. (410 ILCS 210/0.01) (from Ch. 111, par. 4500) Sec. 0.01. Short title. This Act may be cited as the Consent by Minors to Medical Procedures Act. (Source: P.A. 86-1324.) http://www.ilga.gov/legislation/ilcs/ilcs3.asp?ActID=1539&ChapterID=35
   */
  IllinoisConsentByMinorsToMedicalProcedures: consent_policy_IllinoisConsentByMinorsToMedicalProcedures,
  /**
   * On January 1, 2015, the Michigan Department of Health and Human Services (MDHHS) released a standard consent form for the sharing of health information specific to behavioral health and substance use treatment in accordance with Public Act 129 of 2014. In Michigan, while providers are not required to use this new standard form (MDHHS-5515), they are required to accept it. Note: Form is available at http://www.michigan.gov/documents/mdhhs/Consent_to_Share_Behavioral_Health_Information_for_Care_Coordination_Purposes_548835_7.docx For more information see http://www.michigan.gov/documents/mdhhs/Behavioral_Health_Consent_Form_Background_Information_548864_7.pdf
   */
  MichiganMDHHS5515ConsentToShareBehavioralHealthInformationForCareCoordinationPurposes: consent_policy_MichiganMDHHS5515ConsentToShareBehavioralHealthInformationForCareCoordinationPurposes,
  /**
   * see http://ctep.cancer.gov/protocolDevelopment/docs/Informed_Consent_Template.docx
   */
  NCICancerClinicalTrialConsent: consent_policy_NCICancerClinicalTrialConsent,
  /**
   * NIH Authorization for the Release of Medical Information is a patient’s consent for the National Institutes of Health Clinical Center to release medical information to care providers, which can be revoked. Note: Consent Form available @ http://cc.nih.gov/participate/_pdf/NIH-527.pdf
   */
  NIHAuthorizationForTheReleaseOfMedicalInformation: consent_policy_NIHAuthorizationForTheReleaseOfMedicalInformation,
  /**
   * Global Rare Disease Patient Registry and Data Repository (GRDR) consent is an agreement of a healthcare consumer to permit collection, access, use and disclosure of de-identified rare disease information and collection of bio-specimens, medical information, family history and other related information from patients to permit the registry collection of health and genetic information, and specimens for pseudonymized disclosure for research purpose of use. https://rarediseases.info.nih.gov/files/informed_consent_template.pdf
   */
  NIHGlobalRareDiseasePatientRegistryAndDataRepositoryConsent: consent_policy_NIHGlobalRareDiseasePatientRegistryAndDataRepositoryConsent,
  /**
   * Guidance and template form https://privacyruleandresearch.nih.gov/pdf/authorization.pdf
   */
  HHSNIHHIPAAResearchAuthorization: consent_policy_HHSNIHHIPAAResearchAuthorization,
  /**
   * LSP (National Exchange Point) requires that providers, hospitals and pharmacy obtain explicit permission [opt-in] from healthcare consumers to submit and retrieve all or only some of a subject of care’s health information collected by the LSP for purpose of treatment, which can be revoked.  Without permission, a provider cannot access LSP information even in an emergency. The LSP provides healthcare consumers with accountings of disclosures. https://www.vzvz.nl/uploaded/FILES/htmlcontent/Formulieren/TOESTEMMINGSFORMULIER.pdf, https://www.ikgeeftoestemming.nl/en, https://www.ikgeeftoestemming.nl/en/registration/find-healthcare-provider
   */
  NLLSPPermission: consent_policy_NLLSPPermission,
  /**
   * The New York State Surgical and Invasive Procedure Protocol (NYSSIPP) applies to all operative and invasive procedures including endoscopy, general surgery or interventional radiology. Other procedures that involve puncture or incision of the skin, or insertion of an instrument or foreign material into the body are within the scope of the protocol. This protocol also applies to those anesthesia procedures either prior to a surgical procedure or independent of a surgical procedure such as spinal facet blocks. Example: Certain 'minor' procedures such as venipuncture, peripheral IV placement, insertion of nasogastric tube and foley catheter insertion are not within the scope of the protocol. From http://www.health.ny.gov/professionals/protocols_and_guidelines/surgical_and_invasive_procedure/nyssipp_faq.htm Note: HHC 100B-1 Form is available at http://www.downstate.edu/emergency_medicine/documents/Consent_CT_with_contrast.pdf
   */
  NewYorkStateSurgicalAndInvasiveProcedureProtocol: consent_policy_NewYorkStateSurgicalAndInvasiveProcedureProtocol,
  /**
   * Squaxin Indian HIPAA and 42 CFR Part 2 Consent for Release and Exchange of Confidential Information, which permits consenter to select healthcare record type and types of treatment purposes.  This consent requires disclosers and recipients to comply with 42 C.F.R. Part 2, and HIPAA 45 C.F.R. parts 160 and 164. It includes patient notice of the refrain policy not to disclose without consent, and revocation rights. https://www.ncsacw.samhsa.gov/files/SI_ConsentForReleaseAndExchange.PDF
   */
  SquaxinIndianBehavioralHealthAndHIPAAConsent: consent_policy_SquaxinIndianBehavioralHealthAndHIPAAConsent,
  /**
   * SA Form SSA-827 (Authorization to Disclose Information to the Social Security Administration (SSA)). Form is available at https://www.socialsecurity.gov/forms/ssa-827-inst-sp.pdf
   */
  SSAAuthorizationToDisclose: consent_policy_SSAAuthorizationToDisclose,
  /**
   * VA Form 10-0484 Revocation for Release of Individually-Identifiable Health Information enables a veteran to revoke authorization for the VA to release specified copies of individually-identifiable health information with the non-VA health care provider organizations participating in the eHealth Exchange and partnering with VA. Comment: Opt-in Consent Directive with status = rescinded (aka 'revoked'). Note: Form is available at http://www.va.gov/vaforms/medical/pdf/vha-10-0484-fill.pdf
   */
  VAForm100484: consent_policy_VAForm100484,
  /**
   * VA Form 10-0485 Request for and Authorization to Release Protected Health Information to eHealth Exchange enables a veteran to request and authorize a VA health care facility to release protected health information (PHI) for treatment purposes only to the communities that are participating in the eHealth Exchange, VLER Directive, and other Health Information Exchanges with who VA has an agreement. This information may consist of the diagnosis of Sickle Cell Anemia, the treatment of or referral for Drug Abuse, treatment of or referral for Alcohol Abuse or the treatment of or testing for infection with Human Immunodeficiency Virus. This authorization covers the diagnoses that I may have upon signing of the authorization and the diagnoses that I may acquire in the future including those protected by 38 U.S.C. 7332. Comment: Opt-in Consent Directive. Note: Form is available at http://www.va.gov/vaforms/medical/pdf/10-0485-fill.pdf
   */
  VAForm100485: consent_policy_VAForm100485,
  /**
   * VA Form 10-10116 Revocation of Authorization for Use and Release of Individually Identifiable Health Information for Veterans Health Administration Research. Comment: Opt-in with Restriction Consent Directive with status = 'completed'. Note: Form is available at http://www.northerncalifornia.va.gov/northerncalifornia/services/rnd/docs/vha-10-10116.pdf
   */
  VAForm101010116: consent_policy_VAForm101010116,
  /**
   * VA Form 10-5345 Request for and Authorization to Release Medical Records or Health Information enables a veteran to request and authorize the VA to release specified copies of protected health information (PHI), such as hospital summary or outpatient treatment notes, which may include information about conditions governed under Title 38 Section 7332 (drug abuse, alcoholism or alcohol abuse, testing for or infection with HIV, and sickle cell anemia). Comment: Opt-in Consent Directive. Note: Form is available at http://www.va.gov/vaforms/medical/pdf/vha-10-5345-fill.pdf
   */
  VAForm105345: consent_policy_VAForm105345,
  /**
   * VA Form 10-5345a Individuals' Request for a Copy of Their Own Health Information enables a veteran to request and authorize the VA to release specified copies of protected health information (PHI), such as hospital summary or outpatient treatment notes. Note: Form is available at http://www.va.gov/vaforms/medical/pdf/vha-10-5345a-fill.pdf
   */
  VAForm105345a: consent_policy_VAForm105345a,
  /**
   * VA Form 10-5345a-MHV Individual's Request for a Copy of their own health information from MyHealtheVet enables a veteran to receive a copy of all available personal health information to be delivered through the veteran's My HealtheVet account. Note: Form is available at http://www.va.gov/vaforms/medical/pdf/vha-10-5345a-MHV-fill.pdf
   */
  VAForm105345aMHV: consent_policy_VAForm105345aMHV,
  /**
   * VA Form 21-4142 (Authorization and Consent to Release Information to the Department of Veterans Affairs (VA) enables a veteran to authorize the US Veterans Administration [VA] to request veteran's health information from non-VA providers. Aka VA Compensation Application Note: Form is available at http://www.vba.va.gov/pubs/forms/VBA-21-4142-ARE.pdf . For additional information regarding VA Form 21-4142, refer to the following website: www.benefits.va.gov/compensation/consent_privateproviders
   */
  VAForm214142: consent_policy_VAForm214142,
};
const consent_provision_type_OptOut: Coding = {
  code: 'deny',
  display: 'Opt Out',
  system: 'http://hl7.org/fhir/consent-provision-type',
};
const consent_provision_type_OptIn: Coding = {
  code: 'permit',
  display: 'Opt In',
  system: 'http://hl7.org/fhir/consent-provision-type',
};
/**
 * How a rule statement is applied, such as adding additional consent or removing consent.
 */
export const ConsentProvisionType = {
  /**
   * Consent is denied for actions meeting these rules.
   */
  OptOut: consent_provision_type_OptOut,
  /**
   * Consent is provided for actions meeting these rules.
   */
  OptIn: consent_provision_type_OptIn,
};
const consent_scope_AdvancedCareDirective: Coding = {
  code: 'adr',
  display: 'Advanced Care Directive',
  system: 'http://terminology.hl7.org/CodeSystem/consentscope',
};
const consent_scope_PrivacyConsent: Coding = {
  code: 'patient-privacy',
  display: 'Privacy Consent',
  system: 'http://terminology.hl7.org/CodeSystem/consentscope',
};
const consent_scope_Research: Coding = {
  code: 'research',
  display: 'Research',
  system: 'http://terminology.hl7.org/CodeSystem/consentscope',
};
const consent_scope_Treatment: Coding = {
  code: 'treatment',
  display: 'Treatment',
  system: 'http://terminology.hl7.org/CodeSystem/consentscope',
};
/**
 * This value set includes the four Consent scope codes.
 */
export const ConsentScope = {
  /**
   * Actions to be taken if they are no longer able to make decisions for themselves
   */
  AdvancedCareDirective: consent_scope_AdvancedCareDirective,
  /**
   * Agreement to collect, access, use or disclose (share) information
   */
  PrivacyConsent: consent_scope_PrivacyConsent,
  /**
   * Consent to participate in research protocol and information sharing required
   */
  Research: consent_scope_Research,
  /**
   * Consent to undergo a specific treatment
   */
  Treatment: consent_scope_Treatment,
};
const consent_state_codes_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/consent-state-codes',
};
const consent_state_codes_Pending: Coding = {
  code: 'draft',
  display: 'Pending',
  system: 'http://hl7.org/fhir/consent-state-codes',
};
const consent_state_codes_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/consent-state-codes',
};
const consent_state_codes_Inactive: Coding = {
  code: 'inactive',
  display: 'Inactive',
  system: 'http://hl7.org/fhir/consent-state-codes',
};
const consent_state_codes_Proposed: Coding = {
  code: 'proposed',
  display: 'Proposed',
  system: 'http://hl7.org/fhir/consent-state-codes',
};
const consent_state_codes_Rejected: Coding = {
  code: 'rejected',
  display: 'Rejected',
  system: 'http://hl7.org/fhir/consent-state-codes',
};
/**
 * Indicates the state of the consent.
 */
export const ConsentStateCodes = {
  /**
   * The consent is to be followed and enforced.
   */
  Active: consent_state_codes_Active,
  /**
   * The consent is in development or awaiting use but is not yet intended to be acted upon.
   */
  Pending: consent_state_codes_Pending,
  /**
   * The consent was created wrongly (e.g. wrong patient) and should be ignored.
   */
  EnteredInError: consent_state_codes_EnteredInError,
  /**
   * The consent is terminated or replaced.
   */
  Inactive: consent_state_codes_Inactive,
  /**
   * The consent has been proposed but not yet agreed to by all parties. The negotiation stage.
   */
  Proposed: consent_state_codes_Proposed,
  /**
   * The consent has been rejected by one or more of the parties.
   */
  Rejected: consent_state_codes_Rejected,
};
const ConsistencyType_NectarThickLiquid: Coding = {
  code: '439021000124105',
  display: 'nectar thick liquid',
  system: 'http://snomed.info/sct',
};
const ConsistencyType_HoneyThickLiquid: Coding = {
  code: '439031000124108',
  display: 'honey thick liquid',
  system: 'http://snomed.info/sct',
};
const ConsistencyType_SpoonThickLiquid: Coding = {
  code: '439041000124103',
  display: 'spoon thick liquid',
  system: 'http://snomed.info/sct',
};
const ConsistencyType_ThinLiquid: Coding = {
  code: '439081000124109',
  display: 'thin liquid',
  system: 'http://snomed.info/sct',
};
/**
 * FluidConsistencyType :  Codes used to represent the consistency of fluids and liquids provided to the patient. This value set includes concepts from [SNOMED CT](http://snomed.info/sct)(US Extension) where concept is a 435681000124103  (Dietary liquid consistency diet (regime/therapy)). It is provided as a suggestive example.
 */
export const ConsistencyType = {
  NectarThickLiquid: ConsistencyType_NectarThickLiquid,
  HoneyThickLiquid: ConsistencyType_HoneyThickLiquid,
  SpoonThickLiquid: ConsistencyType_SpoonThickLiquid,
  ThinLiquid: ConsistencyType_ThinLiquid,
};
const constraint_severity_Error: Coding = {
  code: 'error',
  display: 'Error',
  system: 'http://hl7.org/fhir/constraint-severity',
};
const constraint_severity_Warning: Coding = {
  code: 'warning',
  display: 'Warning',
  system: 'http://hl7.org/fhir/constraint-severity',
};
/**
 * SHALL applications comply with this constraint?
 */
export const ConstraintSeverity = {
  /**
   * If the constraint is violated, the resource is not conformant.
   */
  Error: constraint_severity_Error,
  /**
   * If the constraint is violated, the resource is conformant, but it is not necessarily following best practice.
   */
  Warning: constraint_severity_Warning,
};
const contactentity_type_Administrative: Coding = {
  code: 'ADMIN',
  display: 'Administrative',
  system: 'http://terminology.hl7.org/CodeSystem/contactentity-type',
};
const contactentity_type_Billing: Coding = {
  code: 'BILL',
  display: 'Billing',
  system: 'http://terminology.hl7.org/CodeSystem/contactentity-type',
};
const contactentity_type_HumanResource: Coding = {
  code: 'HR',
  display: 'Human Resource',
  system: 'http://terminology.hl7.org/CodeSystem/contactentity-type',
};
const contactentity_type_Patient: Coding = {
  code: 'PATINF',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/contactentity-type',
};
const contactentity_type_Payor: Coding = {
  code: 'PAYOR',
  display: 'Payor',
  system: 'http://terminology.hl7.org/CodeSystem/contactentity-type',
};
const contactentity_type_Press: Coding = {
  code: 'PRESS',
  display: 'Press',
  system: 'http://terminology.hl7.org/CodeSystem/contactentity-type',
};
/**
 * This example value set defines a set of codes that can be used to indicate the purpose for which you would contact a contact party.
 */
export const ContactentityType = {
  /**
   * Contact details for administrative enquiries.
   */
  Administrative: contactentity_type_Administrative,
  /**
   * Contact details for information regarding to billing/general finance enquiries.
   */
  Billing: contactentity_type_Billing,
  /**
   * Contact details for issues related to Human Resources, such as staff matters, OH&S etc.
   */
  HumanResource: contactentity_type_HumanResource,
  /**
   * Generic information contact for patients.
   */
  Patient: contactentity_type_Patient,
  /**
   * Contact details for dealing with issues related to insurance claims/adjudication/payment.
   */
  Payor: contactentity_type_Payor,
  /**
   * Dedicated contact point for matters relating to press enquiries.
   */
  Press: contactentity_type_Press,
};
const contact_point_system_Email: Coding = {
  code: 'email',
  display: 'Email',
  system: 'http://hl7.org/fhir/contact-point-system',
};
const contact_point_system_Fax: Coding = {
  code: 'fax',
  display: 'Fax',
  system: 'http://hl7.org/fhir/contact-point-system',
};
const contact_point_system_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://hl7.org/fhir/contact-point-system',
};
const contact_point_system_Pager: Coding = {
  code: 'pager',
  display: 'Pager',
  system: 'http://hl7.org/fhir/contact-point-system',
};
const contact_point_system_Phone: Coding = {
  code: 'phone',
  display: 'Phone',
  system: 'http://hl7.org/fhir/contact-point-system',
};
const contact_point_system_SMS: Coding = {
  code: 'sms',
  display: 'SMS',
  system: 'http://hl7.org/fhir/contact-point-system',
};
const contact_point_system_URL: Coding = {
  code: 'url',
  display: 'URL',
  system: 'http://hl7.org/fhir/contact-point-system',
};
/**
 * Telecommunications form for contact point.
 */
export const ContactPointSystem = {
  /**
   * The value is an email address.
   */
  Email: contact_point_system_Email,
  /**
   * The value is a fax machine. Use of full international numbers starting with + is recommended to enable automatic dialing support but not required.
   */
  Fax: contact_point_system_Fax,
  /**
   * A contact that is not a phone, fax, page or email address and is not expressible as a URL.  E.g. Internal mail address.  This SHOULD NOT be used for contacts that are expressible as a URL (e.g. Skype, Twitter, Facebook, etc.)  Extensions may be used to distinguish "other" contact types.
   */
  Other: contact_point_system_Other,
  /**
   * The value is a pager number. These may be local pager numbers that are only usable on a particular pager system.
   */
  Pager: contact_point_system_Pager,
  /**
   * The value is a telephone number used for voice calls. Use of full international numbers starting with + is recommended to enable automatic dialing support but not required.
   */
  Phone: contact_point_system_Phone,
  /**
   * A contact that can be used for sending an sms message (e.g. mobile phones, some landlines).
   */
  SMS: contact_point_system_SMS,
  /**
   * A contact that is not a phone, fax, pager or email address and is expressed as a URL.  This is intended for various institutional or personal contacts including web sites, blogs, Skype, Twitter, Facebook, etc. Do not use for email addresses.
   */
  URL: contact_point_system_URL,
};
const contact_point_use_Home: Coding = {
  code: 'home',
  display: 'Home',
  system: 'http://hl7.org/fhir/contact-point-use',
};
const contact_point_use_Mobile: Coding = {
  code: 'mobile',
  display: 'Mobile',
  system: 'http://hl7.org/fhir/contact-point-use',
};
const contact_point_use_Old: Coding = {
  code: 'old',
  display: 'Old',
  system: 'http://hl7.org/fhir/contact-point-use',
};
const contact_point_use_Temp: Coding = {
  code: 'temp',
  display: 'Temp',
  system: 'http://hl7.org/fhir/contact-point-use',
};
const contact_point_use_Work: Coding = {
  code: 'work',
  display: 'Work',
  system: 'http://hl7.org/fhir/contact-point-use',
};
/**
 * Use of contact point.
 */
export const ContactPointUse = {
  /**
   * A communication contact point at a home; attempted contacts for business purposes might intrude privacy and chances are one will contact family or other household members instead of the person one wishes to call. Typically used with urgent cases, or if no other contacts are available.
   */
  Home: contact_point_use_Home,
  /**
   * A telecommunication device that moves and stays with its owner. May have characteristics of all other use codes, suitable for urgent matters, not the first choice for routine business.
   */
  Mobile: contact_point_use_Mobile,
  /**
   * This contact point is no longer in use (or was never correct, but retained for records).
   */
  Old: contact_point_use_Old,
  /**
   * A temporary contact point. The period can provide more detailed information.
   */
  Temp: contact_point_use_Temp,
  /**
   * An office contact point. First choice for business related contacts during business hours.
   */
  Work: contact_point_use_Work,
};
const container_cap_BlackCap: Coding = {
  code: 'black',
  display: 'black cap',
  system: 'http://terminology.hl7.org/CodeSystem/container-cap',
};
const container_cap_BrownCap: Coding = {
  code: 'brown',
  display: 'brown cap',
  system: 'http://terminology.hl7.org/CodeSystem/container-cap',
};
const container_cap_DarkYellowCap: Coding = {
  code: 'dark-yellow',
  display: 'dark yellow cap',
  system: 'http://terminology.hl7.org/CodeSystem/container-cap',
};
const container_cap_GreenCap: Coding = {
  code: 'green',
  display: 'green cap',
  system: 'http://terminology.hl7.org/CodeSystem/container-cap',
};
const container_cap_GreyCap: Coding = {
  code: 'grey',
  display: 'grey cap',
  system: 'http://terminology.hl7.org/CodeSystem/container-cap',
};
const container_cap_LavenderCap: Coding = {
  code: 'lavender',
  display: 'lavender cap',
  system: 'http://terminology.hl7.org/CodeSystem/container-cap',
};
const container_cap_LightBlueCap: Coding = {
  code: 'light-blue',
  display: 'light blue cap',
  system: 'http://terminology.hl7.org/CodeSystem/container-cap',
};
const container_cap_LightGreenCap: Coding = {
  code: 'light-green',
  display: 'light green cap',
  system: 'http://terminology.hl7.org/CodeSystem/container-cap',
};
const container_cap_PinkCap: Coding = {
  code: 'pink',
  display: 'pink cap',
  system: 'http://terminology.hl7.org/CodeSystem/container-cap',
};
const container_cap_RedCap: Coding = {
  code: 'red',
  display: 'red cap',
  system: 'http://terminology.hl7.org/CodeSystem/container-cap',
};
const container_cap_WhiteCap: Coding = {
  code: 'white',
  display: 'white cap',
  system: 'http://terminology.hl7.org/CodeSystem/container-cap',
};
const container_cap_YellowCap: Coding = {
  code: 'yellow',
  display: 'yellow cap',
  system: 'http://terminology.hl7.org/CodeSystem/container-cap',
};
/**
 * Color of the container cap.
 */
export const ContainerCap = {
  /**
   * black cap.
   */
  BlackCap: container_cap_BlackCap,
  /**
   * brown cap.
   */
  BrownCap: container_cap_BrownCap,
  /**
   * dark yellow cap.
   */
  DarkYellowCap: container_cap_DarkYellowCap,
  /**
   * green cap.
   */
  GreenCap: container_cap_GreenCap,
  /**
   * grey cap.
   */
  GreyCap: container_cap_GreyCap,
  /**
   * lavender cap.
   */
  LavenderCap: container_cap_LavenderCap,
  /**
   * light blue cap.
   */
  LightBlueCap: container_cap_LightBlueCap,
  /**
   * light green cap.
   */
  LightGreenCap: container_cap_LightGreenCap,
  /**
   * pink cap.
   */
  PinkCap: container_cap_PinkCap,
  /**
   * red cap.
   */
  RedCap: container_cap_RedCap,
  /**
   * white cap.
   */
  WhiteCap: container_cap_WhiteCap,
  /**
   * yellow cap.
   */
  YellowCap: container_cap_YellowCap,
};
const ContainerMaterial_Glass: Coding = {
  code: '32039001',
  display: 'glass',
  system: 'http://snomed.info/sct',
};
const ContainerMaterial_Metal: Coding = {
  code: '425620007',
  display: 'metal',
  system: 'http://snomed.info/sct',
};
const ContainerMaterial_Plastic: Coding = {
  code: '61088005',
  display: 'plastic',
  system: 'http://snomed.info/sct',
};
/**
 * This value set includes SNOMED CT codes for materials that specimen containers are made of
 */
export const ContainerMaterial = {
  Glass: ContainerMaterial_Glass,
  Metal: ContainerMaterial_Metal,
  Plastic: ContainerMaterial_Plastic,
};
const contract_action_ActionA: Coding = {
  code: 'action-a',
  display: 'Action A',
  system: 'http://terminology.hl7.org/CodeSystem/contractaction',
};
const contract_action_ActionB: Coding = {
  code: 'action-b',
  display: 'Action B',
  system: 'http://terminology.hl7.org/CodeSystem/contractaction',
};
/**
 * This value set includes sample Contract Action codes.
 */
export const ContractAction = {
  /**
   * Definition for Action A
   */
  ActionA: contract_action_ActionA,
  /**
   * Definition for Action B
   */
  ActionB: contract_action_ActionB,
};
const contract_actionstatus_Complete: Coding = {
  code: 'complete',
  display: 'Complete',
  system: 'http://hl7.org/fhir/contract-action-status',
};
/**
 * This value set contract specific codes for action status.
 */
export const ContractActionstatus = {
  /**
   * To be completed
   */
  Complete: contract_actionstatus_Complete,
};
const contract_actorrole_Patient: Coding = {
  code: 'patient',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/contractactorrole',
};
const contract_actorrole_Practitioner: Coding = {
  code: 'practitioner',
  display: 'Practitioner',
  system: 'http://terminology.hl7.org/CodeSystem/contractactorrole',
};
/**
 * This value set includes sample Contract Actor Role codes.
 */
export const ContractActorrole = {
  /**
   * A receiver, human or animal, of health care related goods and services.
   */
  Patient: contract_actorrole_Patient,
  /**
   * Someone who provides health care related services to people or animals including both clinical and support services.
   */
  Practitioner: contract_actorrole_Practitioner,
};
const contract_assetcontext_Custodian: Coding = {
  code: 'custodian',
  display: 'Custodian',
  system: 'http://hl7.org/fhir/contract-asset-context',
};
/**
 * This value set contract specific codes for asset context.
 */
export const ContractAssetcontext = {
  /**
   * To be completed
   */
  Custodian: contract_assetcontext_Custodian,
};
const contract_assetscope_Thing: Coding = {
  code: 'thing',
  display: 'Thing',
  system: 'http://hl7.org/fhir/contract-asset-scope',
};
/**
 * This value set contract specific codes for asset scope.
 */
export const ContractAssetscope = {
  /**
   * To be completed
   */
  Thing: contract_assetscope_Thing,
};
const contract_assetsubtype_Participation: Coding = {
  code: 'participation',
  display: 'Participation',
  system: 'http://hl7.org/fhir/contract-asset-subtype',
};
/**
 * This value set contract specific codes for asset subtype.
 */
export const ContractAssetsubtype = {
  /**
   * To be completed
   */
  Participation: contract_assetsubtype_Participation,
};
const contract_assettype_Participation: Coding = {
  code: 'participation',
  display: 'Participation',
  system: 'http://hl7.org/fhir/contract-asset-type',
};
/**
 * This value set contract specific codes for asset type.
 */
export const ContractAssettype = {
  /**
   * To be completed
   */
  Participation: contract_assettype_Participation,
};
const contract_content_derivative_ContentRegistration: Coding = {
  code: 'registration',
  display: 'Content Registration',
  system: 'http://terminology.hl7.org/CodeSystem/contract-content-derivative',
};
const contract_content_derivative_ContentRetrieval: Coding = {
  code: 'retrieval',
  display: 'Content Retrieval',
  system: 'http://terminology.hl7.org/CodeSystem/contract-content-derivative',
};
const contract_content_derivative_ShareableContent: Coding = {
  code: 'shareable',
  display: 'Shareable Content',
  system: 'http://terminology.hl7.org/CodeSystem/contract-content-derivative',
};
const contract_content_derivative_ContentStatement: Coding = {
  code: 'statement',
  display: 'Content Statement',
  system: 'http://terminology.hl7.org/CodeSystem/contract-content-derivative',
};
/**
 * This is an example set of Content Derivative type codes, which represent the minimal content derived from the basal information source at a specific stage in its lifecycle, which is sufficient to manage that source information, for example, in a repository, registry, processes and workflows, for making access control decisions, and providing query responses.
 */
export const ContractContentDerivative = {
  /**
   * Content derivative that conveys sufficient information needed to register the source basal content from which it is derived.  This derivative content may be used to register the basal content as it changes status in its lifecycle.  For example, content registration may occur when the basal content is created, updated, inactive, or deleted.
   */
  ContentRegistration: contract_content_derivative_ContentRegistration,
  /**
   * A content derivative that conveys sufficient information to locate and retrieve the content.
   */
  ContentRetrieval: contract_content_derivative_ContentRetrieval,
  /**
   * A Content Derivative that conveys sufficient information to determine the authorized entities with which the content may be shared.
   */
  ShareableContent: contract_content_derivative_ShareableContent,
  /**
   * Content derivative that has less than full fidelity to the basal information source from which it was 'transcribed'. It provides recipients with the full content representation they may require for compliance purposes, and typically include a reference to or an attached unstructured representation for recipients needing an exact copy of the legal agreement.
   */
  ContentStatement: contract_content_derivative_ContentStatement,
};
const contract_data_meaning_AuthoredBy: Coding = {
  code: 'authoredby',
  display: 'AuthoredBy',
  system: 'http://terminology.hl7.org/CodeSystem/contract-data-meaning',
};
const contract_data_meaning_Dependents: Coding = {
  code: 'dependents',
  display: 'Dependents',
  system: 'http://terminology.hl7.org/CodeSystem/contract-data-meaning',
};
const contract_data_meaning_Instance: Coding = {
  code: 'instance',
  display: 'Instance',
  system: 'http://terminology.hl7.org/CodeSystem/contract-data-meaning',
};
const contract_data_meaning_Related: Coding = {
  code: 'related',
  display: 'Related',
  system: 'http://terminology.hl7.org/CodeSystem/contract-data-meaning',
};
/**
 * How a resource reference is interpreted when evaluating contract offers.
 */
export const ContractDataMeaning = {
  /**
   * The consent applies to instances of resources that are authored by.
   */
  AuthoredBy: contract_data_meaning_AuthoredBy,
  /**
   * The consent applies directly to the instance of the resource and instances that refer to it.
   */
  Dependents: contract_data_meaning_Dependents,
  /**
   * The consent applies directly to the instance of the resource.
   */
  Instance: contract_data_meaning_Instance,
  /**
   * The consent applies directly to the instance of the resource and instances it refers to.
   */
  Related: contract_data_meaning_Related,
};
const contract_decision_mode_Policy: Coding = {
  code: 'policy',
  display: 'Policy',
  system: 'http://hl7.org/fhir/contract-decision-mode',
};
/**
 * This value set contract specific codes for decision modes.
 */
export const ContractDecisionMode = {
  /**
   * To be completed
   */
  Policy: contract_decision_mode_Policy,
};
const contract_definition_subtype_TemporaryValue: Coding = {
  code: 'temp',
  display: 'Temporary Value',
  system: 'http://hl7.org/fhir/contract-definition-subtype',
};
/**
 * This value set contract specific codes for status.
 */
export const ContractDefinitionSubtype = {
  /**
   * To be completed
   */
  TemporaryValue: contract_definition_subtype_TemporaryValue,
};
const contract_definition_type_TemporaryValue: Coding = {
  code: 'temp',
  display: 'Temporary Value',
  system: 'http://hl7.org/fhir/contract-definition-type',
};
/**
 * This value set contract specific codes for status.
 */
export const ContractDefinitionType = {
  /**
   * To be completed
   */
  TemporaryValue: contract_definition_type_TemporaryValue,
};
const contract_expiration_type_Breach: Coding = {
  code: 'breach',
  display: 'Breach',
  system: 'http://hl7.org/fhir/contract-expiration-type',
};
/**
 * This value set contract specific codes for status.
 */
export const ContractExpirationType = {
  /**
   * To be completed
   */
  Breach: contract_expiration_type_Breach,
};
const contract_legalstate_Amended: Coding = {
  code: 'amended',
  display: 'Amended',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_Appended: Coding = {
  code: 'appended',
  display: 'Appended',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_Disputed: Coding = {
  code: 'disputed',
  display: 'Disputed',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_Executable: Coding = {
  code: 'executable',
  display: 'Executable',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_Executed: Coding = {
  code: 'executed',
  display: 'Executed',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_Negotiable: Coding = {
  code: 'negotiable',
  display: 'Negotiable',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_Offered: Coding = {
  code: 'offered',
  display: 'Offered',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_Policy: Coding = {
  code: 'policy',
  display: 'Policy',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_Rejected: Coding = {
  code: 'rejected',
  display: 'Rejected',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_Renewed: Coding = {
  code: 'renewed',
  display: 'Renewed',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_Resolved: Coding = {
  code: 'resolved',
  display: 'Resolved',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_Revoked: Coding = {
  code: 'revoked',
  display: 'Revoked',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
const contract_legalstate_Terminated: Coding = {
  code: 'terminated',
  display: 'Terminated',
  system: 'http://hl7.org/fhir/contract-legalstate',
};
/**
 * This value set contract specific codes for status.
 */
export const ContractLegalstate = {
  /**
   * Contract is augmented with additional information to correct errors in a predecessor or to updated values in a predecessor. Usage: Contract altered within effective time. Precedence Order = 9. Comparable FHIR and v.3 status codes: revised; replaced.
   */
  Amended: contract_legalstate_Amended,
  /**
   * Contract is augmented with additional information that was missing from a predecessor Contract. Usage: Contract altered within effective time. Precedence Order = 9. Comparable FHIR and v.3 status codes: updated, replaced.
   */
  Appended: contract_legalstate_Appended,
  /**
   * Contract is terminated due to failure of the Grantor and/or the Grantee to fulfil one or more contract provisions. Usage: Abnormal contract termination. Precedence Order = 10. Comparable FHIR and v.3 status codes: stopped; failed; aborted.
   */
  Cancelled: contract_legalstate_Cancelled,
  /**
   * Contract is pended to rectify failure of the Grantor or the Grantee to fulfil contract provision(s). E.g., Grantee complaint about Grantor's failure to comply with contract provisions. Usage: Contract pended. Precedence Order = 7. Comparable FHIR and v.3 status codes: on hold; pended; suspended.
   */
  Disputed: contract_legalstate_Disputed,
  /**
   * Contract was created in error. No Precedence Order.  Status may be applied to a Contract with any status.
   */
  EnteredInError: contract_legalstate_EnteredInError,
  /**
   * Contract execution pending; may be executed when either the Grantor or the Grantee accepts the contract provisions by signing. I.e., where either the Grantor or the Grantee has signed, but not both. E.g., when an insurance applicant signs the insurers application, which references the policy. Usage: Optional first step of contract execution activity.  May be skipped and contracting activity moves directly to executed state. Precedence Order = 3. Comparable FHIR and v.3 status codes: draft; preliminary; planned; intended; active.
   */
  Executable: contract_legalstate_Executable,
  /**
   * Contract is activated for period stipulated when both the Grantor and Grantee have signed it. Usage: Required state for normal completion of contracting activity.  Precedence Order = 6. Comparable FHIR and v.3 status codes: accepted; completed.
   */
  Executed: contract_legalstate_Executed,
  /**
   * Contract execution is suspended while either or both the Grantor and Grantee propose and consider new or revised contract provisions. I.e., where the party which has not signed proposes changes to the terms.  E .g., a life insurer declines to agree to the signed application because the life insurer has evidence that the applicant, who asserted to being younger or a non-smoker to get a lower premium rate - but offers instead to agree to a higher premium based on the applicants actual age or smoking status. Usage: Optional contract activity between executable and executed state. Precedence Order = 4. Comparable FHIR and v.3 status codes: in progress; review; held.
   */
  Negotiable: contract_legalstate_Negotiable,
  /**
   * Contract is a proposal by either the Grantor or the Grantee. Aka - A Contract hard copy or electronic 'template', 'form' or 'application'. E.g., health insurance application; consent directive form. Usage: Beginning of contract negotiation, which may have been completed as a precondition because used for 0..* contracts. Precedence Order = 2. Comparable FHIR and v.3 status codes: requested; new.
   */
  Offered: contract_legalstate_Offered,
  /**
   * Contract template is available as the basis for an application or offer by the Grantor or Grantee. E.g., health insurance policy; consent directive policy.  Usage: Required initial contract activity, which may have been completed as a precondition because used for 0..* contracts. Precedence Order = 1. Comparable FHIR and v.3 status codes: proposed; intended.
   */
  Policy: contract_legalstate_Policy,
  /**
   *  Execution of the Contract is not completed because either or both the Grantor and Grantee decline to accept some or all of the contract provisions. Usage: Optional contract activity between executable and abnormal termination. Precedence Order = 5. Comparable FHIR and v.3 status codes:  stopped; cancelled.
   */
  Rejected: contract_legalstate_Rejected,
  /**
   * Beginning of a successor Contract at the termination of predecessor Contract lifecycle. Usage: Follows termination of a preceding Contract that has reached its expiry date. Precedence Order = 13. Comparable FHIR and v.3 status codes: superseded.
   */
  Renewed: contract_legalstate_Renewed,
  /**
   * Contract is reactivated after being pended because of faulty execution. *E.g., competency of the signer(s), or where the policy is substantially different from and did not accompany the application/form so that the applicant could not compare them. Aka - ''reactivated''. Usage: Optional stage where a pended contract is reactivated. Precedence Order = 8. Comparable FHIR and v.3 status codes: reactivated.
   */
  Resolved: contract_legalstate_Resolved,
  /**
   * A Contract that is rescinded.  May be required prior to replacing with an updated Contract. Comparable FHIR and v.3 status codes: nullified.
   */
  Revoked: contract_legalstate_Revoked,
  /**
   * Contract reaches its expiry date. It might or might not be renewed or renegotiated. Usage: Normal end of contract period. Precedence Order = 12. Comparable FHIR and v.3 status codes: Obsoleted.
   */
  Terminated: contract_legalstate_Terminated,
};
const contract_party_role_FLunky: Coding = {
  code: 'flunky',
  display: 'FLunky',
  system: 'http://hl7.org/fhir/contract-party-role',
};
/**
 * This value set contract specific codes for offer party participation.
 */
export const ContractPartyRole = {
  /**
   * To be completed
   */
  FLunky: contract_party_role_FLunky,
};
const contract_publicationstatus_Amended: Coding = {
  code: 'amended',
  display: 'Amended',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_Appended: Coding = {
  code: 'appended',
  display: 'Appended',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_Disputed: Coding = {
  code: 'disputed',
  display: 'Disputed',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_Executable: Coding = {
  code: 'executable',
  display: 'Executable',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_Executed: Coding = {
  code: 'executed',
  display: 'Executed',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_Negotiable: Coding = {
  code: 'negotiable',
  display: 'Negotiable',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_Offered: Coding = {
  code: 'offered',
  display: 'Offered',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_Policy: Coding = {
  code: 'policy',
  display: 'Policy',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_Rejected: Coding = {
  code: 'rejected',
  display: 'Rejected',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_Renewed: Coding = {
  code: 'renewed',
  display: 'Renewed',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_Resolved: Coding = {
  code: 'resolved',
  display: 'Resolved',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_Revoked: Coding = {
  code: 'revoked',
  display: 'Revoked',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
const contract_publicationstatus_Terminated: Coding = {
  code: 'terminated',
  display: 'Terminated',
  system: 'http://hl7.org/fhir/contract-publicationstatus',
};
/**
 * This value set contract specific codes for status.
 */
export const ContractPublicationstatus = {
  /**
   * Contract is augmented with additional information to correct errors in a predecessor or to updated values in a predecessor. Usage: Contract altered within effective time. Precedence Order = 9. Comparable FHIR and v.3 status codes: revised; replaced.
   */
  Amended: contract_publicationstatus_Amended,
  /**
   * Contract is augmented with additional information that was missing from a predecessor Contract. Usage: Contract altered within effective time. Precedence Order = 9. Comparable FHIR and v.3 status codes: updated, replaced.
   */
  Appended: contract_publicationstatus_Appended,
  /**
   * Contract is terminated due to failure of the Grantor and/or the Grantee to fulfil one or more contract provisions. Usage: Abnormal contract termination. Precedence Order = 10. Comparable FHIR and v.3 status codes: stopped; failed; aborted.
   */
  Cancelled: contract_publicationstatus_Cancelled,
  /**
   * Contract is pended to rectify failure of the Grantor or the Grantee to fulfil contract provision(s). E.g., Grantee complaint about Grantor's failure to comply with contract provisions. Usage: Contract pended. Precedence Order = 7. Comparable FHIR and v.3 status codes: on hold; pended; suspended.
   */
  Disputed: contract_publicationstatus_Disputed,
  /**
   * Contract was created in error. No Precedence Order.  Status may be applied to a Contract with any status.
   */
  EnteredInError: contract_publicationstatus_EnteredInError,
  /**
   * Contract execution pending; may be executed when either the Grantor or the Grantee accepts the contract provisions by signing. I.e., where either the Grantor or the Grantee has signed, but not both. E.g., when an insurance applicant signs the insurers application, which references the policy. Usage: Optional first step of contract execution activity.  May be skipped and contracting activity moves directly to executed state. Precedence Order = 3. Comparable FHIR and v.3 status codes: draft; preliminary; planned; intended; active.
   */
  Executable: contract_publicationstatus_Executable,
  /**
   * Contract is activated for period stipulated when both the Grantor and Grantee have signed it. Usage: Required state for normal completion of contracting activity.  Precedence Order = 6. Comparable FHIR and v.3 status codes: accepted; completed.
   */
  Executed: contract_publicationstatus_Executed,
  /**
   * Contract execution is suspended while either or both the Grantor and Grantee propose and consider new or revised contract provisions. I.e., where the party which has not signed proposes changes to the terms.  E .g., a life insurer declines to agree to the signed application because the life insurer has evidence that the applicant, who asserted to being younger or a non-smoker to get a lower premium rate - but offers instead to agree to a higher premium based on the applicants actual age or smoking status. Usage: Optional contract activity between executable and executed state. Precedence Order = 4. Comparable FHIR and v.3 status codes: in progress; review; held.
   */
  Negotiable: contract_publicationstatus_Negotiable,
  /**
   * Contract is a proposal by either the Grantor or the Grantee. Aka - A Contract hard copy or electronic 'template', 'form' or 'application'. E.g., health insurance application; consent directive form. Usage: Beginning of contract negotiation, which may have been completed as a precondition because used for 0..* contracts. Precedence Order = 2. Comparable FHIR and v.3 status codes: requested; new.
   */
  Offered: contract_publicationstatus_Offered,
  /**
   * Contract template is available as the basis for an application or offer by the Grantor or Grantee. E.g., health insurance policy; consent directive policy.  Usage: Required initial contract activity, which may have been completed as a precondition because used for 0..* contracts. Precedence Order = 1. Comparable FHIR and v.3 status codes: proposed; intended.
   */
  Policy: contract_publicationstatus_Policy,
  /**
   *  Execution of the Contract is not completed because either or both the Grantor and Grantee decline to accept some or all of the contract provisions. Usage: Optional contract activity between executable and abnormal termination. Precedence Order = 5. Comparable FHIR and v.3 status codes:  stopped; cancelled.
   */
  Rejected: contract_publicationstatus_Rejected,
  /**
   * Beginning of a successor Contract at the termination of predecessor Contract lifecycle. Usage: Follows termination of a preceding Contract that has reached its expiry date. Precedence Order = 13. Comparable FHIR and v.3 status codes: superseded.
   */
  Renewed: contract_publicationstatus_Renewed,
  /**
   * Contract is reactivated after being pended because of faulty execution. *E.g., competency of the signer(s), or where the policy is substantially different from and did not accompany the application/form so that the applicant could not compare them. Aka - ''reactivated''. Usage: Optional stage where a pended contract is reactivated. Precedence Order = 8. Comparable FHIR and v.3 status codes: reactivated.
   */
  Resolved: contract_publicationstatus_Resolved,
  /**
   * A Contract that is rescinded.  May be required prior to replacing with an updated Contract. Comparable FHIR and v.3 status codes: nullified.
   */
  Revoked: contract_publicationstatus_Revoked,
  /**
   * Contract reaches its expiry date. It might or might not be renewed or renegotiated. Usage: Normal end of contract period. Precedence Order = 12. Comparable FHIR and v.3 status codes: Obsoleted.
   */
  Terminated: contract_publicationstatus_Terminated,
};
const contract_scope_Policy: Coding = {
  code: 'policy',
  display: 'Policy',
  system: 'http://hl7.org/fhir/contract-scope',
};
/**
 * This value set contract specific codes for scope.
 */
export const ContractScope = {
  /**
   * To be completed
   */
  Policy: contract_scope_Policy,
};
const contract_security_category_Policy: Coding = {
  code: 'policy',
  display: 'Policy',
  system: 'http://hl7.org/fhir/contract-security-category',
};
/**
 * This value set contract specific codes for security category.
 */
export const ContractSecurityCategory = {
  /**
   * To be completed
   */
  Policy: contract_security_category_Policy,
};
const contract_security_classification_Policy: Coding = {
  code: 'policy',
  display: 'Policy',
  system: 'http://hl7.org/fhir/contract-security-classification',
};
/**
 * This value set contract specific codes for security classification.
 */
export const ContractSecurityClassification = {
  /**
   * To be completed
   */
  Policy: contract_security_classification_Policy,
};
const contract_security_control_Policy: Coding = {
  code: 'policy',
  display: 'Policy',
  system: 'http://hl7.org/fhir/contract-security-control',
};
/**
 * This value set contract specific codes for security control.
 */
export const ContractSecurityControl = {
  /**
   * To be completed
   */
  Policy: contract_security_control_Policy,
};
const contract_signer_type_Affiliate: Coding = {
  code: 'AFFL',
  display: 'Affiliate',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Agent: Coding = {
  code: 'AGNT',
  display: 'Agent',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Amender: Coding = {
  code: 'AMENDER',
  display: 'Amender',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_AssignedEntity: Coding = {
  code: 'ASSIGNED',
  display: 'Assigned Entity',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Author: Coding = {
  code: 'AUT',
  display: 'Author',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Authenticator: Coding = {
  code: 'AUTHN',
  display: 'Authenticator',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Citizen: Coding = {
  code: 'CIT',
  display: 'Citizen',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Claimant: Coding = {
  code: 'CLAIMANT',
  display: 'Claimant',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_CoAuthor: Coding = {
  code: 'COAUTH',
  display: 'Co-Author',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Consenter: Coding = {
  code: 'CONSENTER',
  display: 'Consenter',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_ConsentWitness: Coding = {
  code: 'CONSWIT',
  display: 'Consent Witness',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Contact: Coding = {
  code: 'CONT',
  display: 'Contact',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_CoParticipant: Coding = {
  code: 'COPART',
  display: 'Co-Participant',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_CoveredParty: Coding = {
  code: 'COVPTY',
  display: 'Covered Party',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Delegatee: Coding = {
  code: 'DELEGATEE',
  display: 'Delegatee',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Delegator: Coding = {
  code: 'delegator',
  display: 'Delegator',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Dependent: Coding = {
  code: 'DEPEND',
  display: 'Dependent',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_DurablePowerOfAttorney: Coding = {
  code: 'DPOWATT',
  display: 'Durable Power of Attorney',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_EmergencyContact: Coding = {
  code: 'EMGCON',
  display: 'Emergency Contact',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_EventWitness: Coding = {
  code: 'EVTWIT',
  display: 'Event Witness',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_ExecutorOfEstate: Coding = {
  code: 'EXCEST',
  display: 'Executor of Estate',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Grantee: Coding = {
  code: 'GRANTEE',
  display: 'Grantee',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Grantor: Coding = {
  code: 'GRANTOR',
  display: 'Grantor',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_GuardianAdLidem: Coding = {
  code: 'GUADLTM',
  display: 'Guardian ad lidem',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Guarantor: Coding = {
  code: 'GUAR',
  display: 'Guarantor',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Guardian: Coding = {
  code: 'GUARD',
  display: 'Guardian',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_HealthcarePowerOfAttorney: Coding = {
  code: 'HPOWATT',
  display: 'Healthcare Power of Attorney',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_HealthcareProvider: Coding = {
  code: 'HPROV',
  display: 'Healthcare Provider',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Informant: Coding = {
  code: 'INF',
  display: 'Informant',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_InvestigationSubject: Coding = {
  code: 'INSBJ',
  display: 'Investigation Subject',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Interpreter: Coding = {
  code: 'INTPRT',
  display: 'Interpreter',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_LegalAuthenticator: Coding = {
  code: 'LEGAUTHN',
  display: 'Legal Authenticator',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_NamedInsured: Coding = {
  code: 'NMDINS',
  display: 'Named Insured',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_NextOfKin: Coding = {
  code: 'NOK',
  display: 'Next of Kin',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Notary: Coding = {
  code: 'NOTARY',
  display: 'Notary',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Patient: Coding = {
  code: 'PAT',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_PowerOfAttorney: Coding = {
  code: 'POWATT',
  display: 'Power of Attorney',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_PrimaryAuthor: Coding = {
  code: 'PRIMAUTH',
  display: 'Primary Author',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_PrimaryResponsibleParty: Coding = {
  code: 'PRIRECIP',
  display: 'Primary Responsible Party ',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Recipient: Coding = {
  code: 'RECIP',
  display: 'Recipient',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_ResponsibleParty: Coding = {
  code: 'RESPRSN',
  display: 'Responsible Party',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Reviewer: Coding = {
  code: 'REVIEWER',
  display: 'Reviewer',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Source: Coding = {
  code: 'SOURCE',
  display: 'Source',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_SpecialPowerOfAttorney: Coding = {
  code: 'SPOWATT',
  display: 'Special Power of Attorney',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Transcriber: Coding = {
  code: 'TRANS',
  display: 'Transcriber',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Validator: Coding = {
  code: 'VALID',
  display: 'Validator',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Verifier: Coding = {
  code: 'VERF',
  display: 'Verifier',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
const contract_signer_type_Witness: Coding = {
  code: 'WIT',
  display: 'Witness',
  system: 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
};
/**
 * This value set includes sample Contract Signer Type codes.
 */
export const ContractSignerType = {
  /**
   * An entity that has a business or professional relationship with another entity in accordance with an agreement.
   */
  Affiliate: contract_signer_type_Affiliate,
  /**
   * An entity that acts or is authorized to act on behalf of another entity in accordance with an agreement.
   */
  Agent: contract_signer_type_Agent,
  /**
   * A person who has corrected, edited, or amended pre-existing information.
   */
  Amender: contract_signer_type_Amender,
  /**
   * An agent role in which the agent is an Entity acting in the employ of an organization. The focus is on functional role on behalf of the organization, unlike the Employee role where the focus is on the 'Human Resources' relationship between the employee and the organization.
   */
  AssignedEntity: contract_signer_type_AssignedEntity,
  /**
   * An entity that authored specific content. There can be multiple authors of content, which may take such forms as a contract, a healthcare record entry or document, a policy, or a consent directive.
   */
  Author: contract_signer_type_Author,
  /**
   * A person in the role of verifier who attests to the accuracy of an act, but who does not have privileges to legally authenticate information content. An example would be a resident physician who sees a patient and dictates a note, then later signs it. The resident's signature constitutes an authentication.
   */
  Authenticator: contract_signer_type_Authenticator,
  /**
   * The member of a jurisdiction afforded certain rights and encumbered with certain obligation in accordance with jurisdictional policy.
   */
  Citizen: contract_signer_type_Citizen,
  /**
   * A party that makes a claim for coverage under a policy.
   */
  Claimant: contract_signer_type_Claimant,
  /**
   * The entity that co-authored content. There can be multiple co-authors of content, which may take such forms as a contract, a healthcare record entry or document, a policy, or a consent directive.
   */
  CoAuthor: contract_signer_type_CoAuthor,
  /**
   * A patient or patient representative who is the grantee in a healthcare related agreement such as a consent for healthcare services, advanced directive, or a privacy consent directive in accordance with jurisdictional, organizational, or patient policy.
   */
  Consenter: contract_signer_type_Consenter,
  /**
   * A person who has witnessed and attests to observing a patient being counseled about a healthcare related agreement such as a consent for healthcare services, advanced directive, or a privacy consent directive.
   */
  ConsentWitness: contract_signer_type_ConsentWitness,
  /**
   * A person or an organization that provides or receives information regarding another entity. Examples; patient NOK and emergency contacts; guarantor contact; employer contact.
   */
  Contact: contract_signer_type_Contact,
  /**
   * A person who participates in the generation of and attest to veracity of content but is not an author or co-author. For example, a surgeon who is required by institutional, regulatory, or legal rules to sign an operative report, but who was not involved in the authorship of that report.
   */
  CoParticipant: contract_signer_type_CoParticipant,
  /**
   * An entity, which is the insured, that receives benefits such as healthcare services, reimbursement for out-of-pocket expenses, or compensation for losses through coverage under the terms of an insurance policy. The underwriter of that policy is the scoping entity. The covered party receives coverage because of some contractual or other relationship with the holder of that policy. Note that a particular policy may cover several individuals one of whom may be, but need not be, the policy holder. Thus the notion of covered party is a role that is distinct from that of the policy holder.
   */
  CoveredParty: contract_signer_type_CoveredParty,
  /**
   * A party to whom some right or authority is delegated by a delegator.
   */
  Delegatee: contract_signer_type_Delegatee,
  /**
   * A party that delegates a right or authority to another party.
   */
  Delegator: contract_signer_type_Delegator,
  /**
   * A person covered under an insurance policy or program based on an association with a subscriber, which is recognized by the policy holder. The dependent has an association with the subscriber such as a financial dependency or personal relationship such as that of a spouse, or a natural or adopted child. The policy holder may be required by law to recognize certain associations or may have discretion about the associations. For example, a policy holder may dictate the criteria for the dependent status of adult children who are students, such as requiring full time enrollment, or may recognize domestic partners as dependents. Under certain circumstances, the dependent may be under the indirect authority of a responsible party acting as a surrogate for the subscriber, for example, if the subscriber is differently-abled or deceased, a guardian ad lidem or estate executor may be appointed to assume the subscriber's legal standing in the relationship with the dependent.
   */
  Dependent: contract_signer_type_Dependent,
  /**
   * A person who has been granted the authority to represent or act on another's behalf generally in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts. Unlike ordinary powers of attorney, durable powers can survive for long periods of time, and again, unlike standard powers of attorney, durable powers can continue after incompetency.
   */
  DurablePowerOfAttorney: contract_signer_type_DurablePowerOfAttorney,
  /**
   * An entity to be contacted in the event of an emergency
   */
  EmergencyContact: contract_signer_type_EmergencyContact,
  /**
   * A person who attests to observing an occurrence.  For example, the witness has observed a procedure and is attesting to this fact.
   */
  EventWitness: contract_signer_type_EventWitness,
  /**
   * A person who has been granted the authority to act as an estate executor for a deceased person who was the responsible party.
   */
  ExecutorOfEstate: contract_signer_type_ExecutorOfEstate,
  /**
   * A person who grants to another person the authority to represent or act on that person's behalf.  Examples include (1) exercising specific rights belonging to the grantee; (2) performing specific duties on behalf of a grantee; and (3) making specific decisions concerning a grantee.
   */
  Grantee: contract_signer_type_Grantee,
  /**
   * A person who has been granted the authority to represent or act on another's behalf. Examples include (1) exercising specific rights belonging to the grantee; (2) performing specific duties on behalf of a grantee; and (3) making specific decisions concerning a grantee.
   */
  Grantor: contract_signer_type_Grantor,
  /**
   * A person appointed by the court to look out for the best interests of a minor child during the course of legal proceedings.
   */
  GuardianAdLidem: contract_signer_type_GuardianAdLidem,
  /**
   * A person or organization contractually recognized by the issuer as an entity that has assumed fiscal responsibility (e.g., by making or giving a promise, assurance, or pledge) for another entity's financial obligations by guaranteeing to pay for amounts owed to a particular account.  In a healthcare context, the account may be a patient's billing account for services rendered by a provider or a health plan premium account.
   */
  Guarantor: contract_signer_type_Guarantor,
  /**
   * A person or organization legally empowered with responsibility for the care of a ward.
   */
  Guardian: contract_signer_type_Guardian,
  /**
   * A person who has been granted the authority to represent or act on another's behalf for healthcare related matters in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts. Examples include (1) exercising specific healthcare legal rights belonging to the grantee such as signing a consent directive; (2) performing specific healthcare related legal duties on behalf of a grantee such as claims payment; and (3) making specific healthcare legal decisions concerning a grantee such as consenting to healthcare services.
   */
  HealthcarePowerOfAttorney: contract_signer_type_HealthcarePowerOfAttorney,
  /**
   * An entity that is authorized to provide health care services by an authorizing organization or jurisdiction.
   */
  HealthcareProvider: contract_signer_type_HealthcareProvider,
  /**
   * An entity that is the source of reported information (e.g., a next of kin who answers questions about the patient's history). For history questions, the patient is logically an informant, yet the informant of history questions is implicitly the subject.
   */
  Informant: contract_signer_type_Informant,
  /**
   * An entity that is the subject of an investigation. This role is scoped by the party responsible for the investigation.
   */
  InvestigationSubject: contract_signer_type_InvestigationSubject,
  /**
   * A person who converts spoken or written language into the language of key participants in an event such as when a provider is obtaining a patient's consent to treatment or permission to disclose information.
   */
  Interpreter: contract_signer_type_Interpreter,
  /**
   * A person in the role of verifier who attests to the accuracy of information content, and who has privileges to certify the legal authenticity of that content with a signature that constitutes a legal authentication.  For example, a licensed physician who signs a consult authored by a resident physician who authenticated it.
   */
  LegalAuthenticator: contract_signer_type_LegalAuthenticator,
  /**
   * A party to an insurance policy under which the insurer agrees to indemnify for losses, provides benefits for, or renders services. A named insured may be either a person, non-person living subject, or an organization, or a group of persons, non-person living subject that is the named insured under a comprehensive automobile, disability, or property and casualty policy.  The named insured and might or might not be the policy holder.
   */
  NamedInsured: contract_signer_type_NamedInsured,
  /**
   * A person, who is a type of contact, designated to receive notifications on behalf of another person who is a relative.
   */
  NextOfKin: contract_signer_type_NextOfKin,
  /**
   * The party credentialed to legally attest to the contract binding by verifying the identity and capacity of the grantor and grantee, and witnessing their signing of the contract or agreement such as a real estate transaction, pre-nuptial agreement, or a will.
   */
  Notary: contract_signer_type_Notary,
  /**
   * A person, animal, or other living subject that is the actual or potential recipient of health care services.
   */
  Patient: contract_signer_type_Patient,
  /**
   * A person who has been granted the authority to represent or act on another's behalf generally in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts. Examples include (1) exercising specific legal rights belonging to the grantee such as signing a contract; (2) performing specific legal duties on behalf of a grantee such as making loan payments; and (3) making specific legal decisions concerning a grantee such as financial investment decisions.
   */
  PowerOfAttorney: contract_signer_type_PowerOfAttorney,
  /**
   * An entity that is the primary or sole author of information content.  In the healthcare context, there can be only one primary author of health information content in a record entry or document.
   */
  PrimaryAuthor: contract_signer_type_PrimaryAuthor,
  /**
   * An entity that may, should receive, or has received information or an object to which it was primarily addressed.
   */
  PrimaryResponsibleParty: contract_signer_type_PrimaryResponsibleParty,
  /**
   * An entity that may, should receive, or has received information or an object, which might not have been primarily addressed to it. For example, the staff of a provider, a clearinghouse, or other intermediary.
   */
  Recipient: contract_signer_type_Recipient,
  /**
   * An entity that has legal responsibility for another party.
   */
  ResponsibleParty: contract_signer_type_ResponsibleParty,
  /**
   * A person, device, or algorithm that has used approved criteria for filtered data for inclusion into the patient record.  Examples: (1) a medical records clerk who scans a document for inclusion in the medical record, enters header information, or catalogues and classifies the data, or a combination thereof; (2) a gateway that receives data from another computer system and interprets that data or changes its format, or both, before entering it into the patient record.
   */
  Reviewer: contract_signer_type_Reviewer,
  /**
   * An automated data source that generates a signature along with content. Examples: (1) the signature for an image that is generated by a device for inclusion in the patient record; (2) the signature for an ECG derived by an ECG system for inclusion in the patient record; (3) the data from a biomedical monitoring device or system that is for inclusion in the patient record.
   */
  Source: contract_signer_type_Source,
  /**
   * A person who has been granted the authority to represent or act on another's behalf for a limited set of specific matters in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts. Examples include (1) exercising specific legal rights belonging to the grantee such as drafting a will; (2) performing specific legal duties on behalf of a grantee such as making a reversible mortgage to pay for end of life expenses; and (3) making specific legal decisions concerning a grantee such as managing a trust.
   */
  SpecialPowerOfAttorney: contract_signer_type_SpecialPowerOfAttorney,
  /**
   * An entity entering the data into the originating system. This includes the transcriptionist for dictated text transcribed into electronic form.
   */
  Transcriber: contract_signer_type_Transcriber,
  /**
   * A person who validates a health information document for inclusion in the patient record. For example, a medical student or resident is credentialed to perform history or physical examinations and to write progress notes. The attending physician signs the history and physical examination to validate the entry for inclusion in the patient's medical record.
   */
  Validator: contract_signer_type_Validator,
  /**
   * A person who asserts the correctness and appropriateness of an act or the recording of the act, and is accountable for the assertion that the act or the recording of the act complies with jurisdictional or organizational policy. For example, a physician is required to countersign a verbal order that has previously been recorded in the medical record by a registered nurse who has carried out the verbal order.
   */
  Verifier: contract_signer_type_Verifier,
  /**
   * A person witnessing the signature of another party. A witness is not knowledgeable about the content being signed, much less approves of anything stated in the content. For example, an advanced directive witness or a witness that a party to a contract signed that certain demographic or financial information is truthful.
   */
  Witness: contract_signer_type_Witness,
};
const contract_status_Amended: Coding = {
  code: 'amended',
  display: 'Amended',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_Appended: Coding = {
  code: 'appended',
  display: 'Appended',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_Disputed: Coding = {
  code: 'disputed',
  display: 'Disputed',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_Executable: Coding = {
  code: 'executable',
  display: 'Executable',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_Executed: Coding = {
  code: 'executed',
  display: 'Executed',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_Negotiable: Coding = {
  code: 'negotiable',
  display: 'Negotiable',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_Offered: Coding = {
  code: 'offered',
  display: 'Offered',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_Policy: Coding = {
  code: 'policy',
  display: 'Policy',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_Rejected: Coding = {
  code: 'rejected',
  display: 'Rejected',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_Renewed: Coding = {
  code: 'renewed',
  display: 'Renewed',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_Resolved: Coding = {
  code: 'resolved',
  display: 'Resolved',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_Revoked: Coding = {
  code: 'revoked',
  display: 'Revoked',
  system: 'http://hl7.org/fhir/contract-status',
};
const contract_status_Terminated: Coding = {
  code: 'terminated',
  display: 'Terminated',
  system: 'http://hl7.org/fhir/contract-status',
};
/**
 * This value set contract specific codes for status.
 */
export const ContractStatus = {
  /**
   * Contract is augmented with additional information to correct errors in a predecessor or to updated values in a predecessor. Usage: Contract altered within effective time. Precedence Order = 9. Comparable FHIR and v.3 status codes: revised; replaced.
   */
  Amended: contract_status_Amended,
  /**
   * Contract is augmented with additional information that was missing from a predecessor Contract. Usage: Contract altered within effective time. Precedence Order = 9. Comparable FHIR and v.3 status codes: updated, replaced.
   */
  Appended: contract_status_Appended,
  /**
   * Contract is terminated due to failure of the Grantor and/or the Grantee to fulfil one or more contract provisions. Usage: Abnormal contract termination. Precedence Order = 10. Comparable FHIR and v.3 status codes: stopped; failed; aborted.
   */
  Cancelled: contract_status_Cancelled,
  /**
   * Contract is pended to rectify failure of the Grantor or the Grantee to fulfil contract provision(s). E.g., Grantee complaint about Grantor's failure to comply with contract provisions. Usage: Contract pended. Precedence Order = 7. Comparable FHIR and v.3 status codes: on hold; pended; suspended.
   */
  Disputed: contract_status_Disputed,
  /**
   * Contract was created in error. No Precedence Order.  Status may be applied to a Contract with any status.
   */
  EnteredInError: contract_status_EnteredInError,
  /**
   * Contract execution pending; may be executed when either the Grantor or the Grantee accepts the contract provisions by signing. I.e., where either the Grantor or the Grantee has signed, but not both. E.g., when an insurance applicant signs the insurers application, which references the policy. Usage: Optional first step of contract execution activity.  May be skipped and contracting activity moves directly to executed state. Precedence Order = 3. Comparable FHIR and v.3 status codes: draft; preliminary; planned; intended; active.
   */
  Executable: contract_status_Executable,
  /**
   * Contract is activated for period stipulated when both the Grantor and Grantee have signed it. Usage: Required state for normal completion of contracting activity.  Precedence Order = 6. Comparable FHIR and v.3 status codes: accepted; completed.
   */
  Executed: contract_status_Executed,
  /**
   * Contract execution is suspended while either or both the Grantor and Grantee propose and consider new or revised contract provisions. I.e., where the party which has not signed proposes changes to the terms.  E .g., a life insurer declines to agree to the signed application because the life insurer has evidence that the applicant, who asserted to being younger or a non-smoker to get a lower premium rate - but offers instead to agree to a higher premium based on the applicants actual age or smoking status. Usage: Optional contract activity between executable and executed state. Precedence Order = 4. Comparable FHIR and v.3 status codes: in progress; review; held.
   */
  Negotiable: contract_status_Negotiable,
  /**
   * Contract is a proposal by either the Grantor or the Grantee. Aka - A Contract hard copy or electronic 'template', 'form' or 'application'. E.g., health insurance application; consent directive form. Usage: Beginning of contract negotiation, which may have been completed as a precondition because used for 0..* contracts. Precedence Order = 2. Comparable FHIR and v.3 status codes: requested; new.
   */
  Offered: contract_status_Offered,
  /**
   * Contract template is available as the basis for an application or offer by the Grantor or Grantee. E.g., health insurance policy; consent directive policy.  Usage: Required initial contract activity, which may have been completed as a precondition because used for 0..* contracts. Precedence Order = 1. Comparable FHIR and v.3 status codes: proposed; intended.
   */
  Policy: contract_status_Policy,
  /**
   *  Execution of the Contract is not completed because either or both the Grantor and Grantee decline to accept some or all of the contract provisions. Usage: Optional contract activity between executable and abnormal termination. Precedence Order = 5. Comparable FHIR and v.3 status codes:  stopped; cancelled.
   */
  Rejected: contract_status_Rejected,
  /**
   * Beginning of a successor Contract at the termination of predecessor Contract lifecycle. Usage: Follows termination of a preceding Contract that has reached its expiry date. Precedence Order = 13. Comparable FHIR and v.3 status codes: superseded.
   */
  Renewed: contract_status_Renewed,
  /**
   * Contract is reactivated after being pended because of faulty execution. *E.g., competency of the signer(s), or where the policy is substantially different from and did not accompany the application/form so that the applicant could not compare them. Aka - ''reactivated''. Usage: Optional stage where a pended contract is reactivated. Precedence Order = 8. Comparable FHIR and v.3 status codes: reactivated.
   */
  Resolved: contract_status_Resolved,
  /**
   * A Contract that is rescinded.  May be required prior to replacing with an updated Contract. Comparable FHIR and v.3 status codes: nullified.
   */
  Revoked: contract_status_Revoked,
  /**
   * Contract reaches its expiry date. It might or might not be renewed or renegotiated. Usage: Normal end of contract period. Precedence Order = 12. Comparable FHIR and v.3 status codes: Obsoleted.
   */
  Terminated: contract_status_Terminated,
};
const contract_subtype_DisclosureCA: Coding = {
  code: 'disclosure-ca',
  display: 'Disclosure-CA',
  system: 'http://terminology.hl7.org/CodeSystem/contractsubtypecodes',
};
const contract_subtype_DisclosureUS: Coding = {
  code: 'disclosure-us',
  display: 'Disclosure-US',
  system: 'http://terminology.hl7.org/CodeSystem/contractsubtypecodes',
};
/**
 * This value set includes sample Contract Subtype codes.
 */
export const ContractSubtype = {
  /**
   * Canadian health information disclosure policy.
   */
  DisclosureCA: contract_subtype_DisclosureCA,
  /**
   * United States health information disclosure policy.
   */
  DisclosureUS: contract_subtype_DisclosureUS,
};
const contract_term_subtype_Condition: Coding = {
  code: 'condition',
  display: 'Condition',
  system: 'http://terminology.hl7.org/CodeSystem/contracttermsubtypecodes',
};
const contract_term_subtype_Innominate: Coding = {
  code: 'innominate',
  display: 'Innominate',
  system: 'http://terminology.hl7.org/CodeSystem/contracttermsubtypecodes',
};
const contract_term_subtype_Warranty: Coding = {
  code: 'warranty',
  display: 'Warranty',
  system: 'http://terminology.hl7.org/CodeSystem/contracttermsubtypecodes',
};
/**
 * This value set includes sample Contract Term SubType codes.
 */
export const ContractTermSubtype = {
  /**
   * Terms that go to the very root of a contract.
   */
  Condition: contract_term_subtype_Condition,
  /**
   * Breach of which might or might not go to the root of the contract depending upon the nature of the breach
   */
  Innominate: contract_term_subtype_Innominate,
  /**
   * Less imperative than a condition, so the contract will survive a breach
   */
  Warranty: contract_term_subtype_Warranty,
};
const contract_term_type_Statutory: Coding = {
  code: 'statutory',
  display: 'Statutory',
  system: 'http://terminology.hl7.org/CodeSystem/contracttermtypecodes',
};
const contract_term_type_SubjectTo: Coding = {
  code: 'subject-to',
  display: 'Subject To',
  system: 'http://terminology.hl7.org/CodeSystem/contracttermtypecodes',
};
/**
 * This value set includes sample Contract Term Type codes.
 */
export const ContractTermType = {
  /**
   * Based on specialized statutes that deal with particular subjects.
   */
  Statutory: contract_term_type_Statutory,
  /**
   * Execution of the term in the contract is conditional on the execution of other actions.
   */
  SubjectTo: contract_term_type_SubjectTo,
};
const contract_type_Consent: Coding = {
  code: 'consent',
  display: 'Consent',
  system: 'http://terminology.hl7.org/CodeSystem/contract-type',
};
const contract_type_Disclosure: Coding = {
  code: 'disclosure',
  display: 'Disclosure',
  system: 'http://terminology.hl7.org/CodeSystem/contract-type',
};
const contract_type_HealthInsurance: Coding = {
  code: 'healthinsurance',
  display: 'Health Insurance',
  system: 'http://terminology.hl7.org/CodeSystem/contract-type',
};
const contract_type_Privacy: Coding = {
  code: 'privacy',
  display: 'Privacy',
  system: 'http://terminology.hl7.org/CodeSystem/contract-type',
};
const contract_type_SupplyContract: Coding = {
  code: 'supply',
  display: 'Supply Contract',
  system: 'http://terminology.hl7.org/CodeSystem/contract-type',
};
/**
 * This value set includes sample Contract Type codes.
 */
export const ContractType = {
  /**
   * Consent Directive.
   */
  Consent: contract_type_Consent,
  /**
   * Information disclosure policy.
   */
  Disclosure: contract_type_Disclosure,
  /**
   * Health Insurance policy.
   */
  HealthInsurance: contract_type_HealthInsurance,
  /**
   * Privacy policy.
   */
  Privacy: contract_type_Privacy,
  /**
   * Contract to supply goods or services.
   */
  SupplyContract: contract_type_SupplyContract,
};
const contributor_type_Author: Coding = {
  code: 'author',
  display: 'Author',
  system: 'http://hl7.org/fhir/contributor-type',
};
const contributor_type_Editor: Coding = {
  code: 'editor',
  display: 'Editor',
  system: 'http://hl7.org/fhir/contributor-type',
};
const contributor_type_Endorser: Coding = {
  code: 'endorser',
  display: 'Endorser',
  system: 'http://hl7.org/fhir/contributor-type',
};
const contributor_type_Reviewer: Coding = {
  code: 'reviewer',
  display: 'Reviewer',
  system: 'http://hl7.org/fhir/contributor-type',
};
/**
 * The type of contributor.
 */
export const ContributorType = {
  /**
   * An author of the content of the module.
   */
  Author: contributor_type_Author,
  /**
   * An editor of the content of the module.
   */
  Editor: contributor_type_Editor,
  /**
   * An endorser of the content of the module.
   */
  Endorser: contributor_type_Endorser,
  /**
   * A reviewer of the content of the module.
   */
  Reviewer: contributor_type_Reviewer,
};
const copy_number_event_Amplification: Coding = {
  code: 'amp',
  display: 'amplification',
  system: 'http://terminology.hl7.org/CodeSystem/copy-number-event',
};
const copy_number_event_Deletion: Coding = {
  code: 'del',
  display: 'deletion',
  system: 'http://terminology.hl7.org/CodeSystem/copy-number-event',
};
const copy_number_event_LossOfFunction: Coding = {
  code: 'lof',
  display: 'loss of function',
  system: 'http://terminology.hl7.org/CodeSystem/copy-number-event',
};
/**
 * Copy Number Event.
 */
export const CopyNumberEvent = {
  /**
   * amplification.
   */
  Amplification: copy_number_event_Amplification,
  /**
   * deletion.
   */
  Deletion: copy_number_event_Deletion,
  /**
   * loss of function.
   */
  LossOfFunction: copy_number_event_LossOfFunction,
};
const coverage_class_Class: Coding = {
  code: 'class',
  display: 'Class',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
};
const coverage_class_Group: Coding = {
  code: 'group',
  display: 'Group',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
};
const coverage_class_Plan: Coding = {
  code: 'plan',
  display: 'Plan',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
};
const coverage_class_RXBIN: Coding = {
  code: 'rxbin',
  display: 'RX BIN',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
};
const coverage_class_RXGroup: Coding = {
  code: 'rxgroup',
  display: 'RX Group',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
};
const coverage_class_RXId: Coding = {
  code: 'rxid',
  display: 'RX Id',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
};
const coverage_class_RXPCN: Coding = {
  code: 'rxpcn',
  display: 'RX PCN',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
};
const coverage_class_Sequence: Coding = {
  code: 'sequence',
  display: 'Sequence',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
};
const coverage_class_SubClass: Coding = {
  code: 'subclass',
  display: 'SubClass',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
};
const coverage_class_SubGroup: Coding = {
  code: 'subgroup',
  display: 'SubGroup',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
};
const coverage_class_SubPlan: Coding = {
  code: 'subplan',
  display: 'SubPlan',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
};
/**
 * This value set includes Coverage Class codes.
 */
export const CoverageClass = {
  /**
   * A class of benefits.
   */
  Class: coverage_class_Class,
  /**
   * An employee group
   */
  Group: coverage_class_Group,
  /**
   * A specific suite of benefits.
   */
  Plan: coverage_class_Plan,
  /**
   * Pharmacy benefit manager's Business Identification Number.
   */
  RXBIN: coverage_class_RXBIN,
  /**
   * A Pharmacy Benefit Manager specified Group number.
   */
  RXGroup: coverage_class_RXGroup,
  /**
   * A Pharmacy Benefit Manager specified Member ID.
   */
  RXId: coverage_class_RXId,
  /**
   * A Pharmacy Benefit Manager specified Processor Control Number.
   */
  RXPCN: coverage_class_RXPCN,
  /**
   * A sequence number associated with a short-term continuance of the coverage.
   */
  Sequence: coverage_class_Sequence,
  /**
   * A subset of a class of benefits.
   */
  SubClass: coverage_class_SubClass,
  /**
   * A sub-group of an employee group
   */
  SubGroup: coverage_class_SubGroup,
  /**
   * A subset of a specific suite of benefits.
   */
  SubPlan: coverage_class_SubPlan,
};
const coverage_copay_type_CopayAmount: Coding = {
  code: 'copay',
  display: 'Copay Amount',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-copay-type',
};
const coverage_copay_type_CopayPercentage: Coding = {
  code: 'copaypct',
  display: 'Copay Percentage',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-copay-type',
};
const coverage_copay_type_Deductible: Coding = {
  code: 'deductible',
  display: 'Deductible',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-copay-type',
};
const coverage_copay_type_Emergency: Coding = {
  code: 'emergency',
  display: 'Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-copay-type',
};
const coverage_copay_type_GPOfficeVisit: Coding = {
  code: 'gpvisit',
  display: 'GP Office Visit',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-copay-type',
};
const coverage_copay_type_InpatientHospital: Coding = {
  code: 'inpthosp',
  display: 'Inpatient Hospital',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-copay-type',
};
const coverage_copay_type_MaximumOutOfPocket: Coding = {
  code: 'maxoutofpocket',
  display: 'Maximum out of pocket',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-copay-type',
};
const coverage_copay_type_SpecialistOfficeVisit: Coding = {
  code: 'spvisit',
  display: 'Specialist Office Visit',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-copay-type',
};
const coverage_copay_type_TeleVisit: Coding = {
  code: 'televisit',
  display: 'Tele-visit',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-copay-type',
};
const coverage_copay_type_UrgentCare: Coding = {
  code: 'urgentcare',
  display: 'Urgent Care',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-copay-type',
};
/**
 * This value set includes sample Coverage Copayment Type codes.
 */
export const CoverageCopayType = {
  /**
   * A standard fixed currency amount applied to all classes or service or product not otherwise specified.
   */
  CopayAmount: coverage_copay_type_CopayAmount,
  /**
   * A standard percentage applied to all classes or service or product not otherwise specified.
   */
  CopayPercentage: coverage_copay_type_CopayPercentage,
  /**
   * The accumulated amount of patient payment before the coverage begins to pay for services.
   */
  Deductible: coverage_copay_type_Deductible,
  /**
   * An episode in an emergency department.
   */
  Emergency: coverage_copay_type_Emergency,
  /**
   * An office visit for a general practitioner of a discipline.
   */
  GPOfficeVisit: coverage_copay_type_GPOfficeVisit,
  /**
   * An episode of an Inpatient hospital stay.
   */
  InpatientHospital: coverage_copay_type_InpatientHospital,
  /**
   * The maximum amout of payment for services which a patient, or family, is expected to incur - typically annually.
   */
  MaximumOutOfPocket: coverage_copay_type_MaximumOutOfPocket,
  /**
   * An office visit for a specialist practitioner of a discipline
   */
  SpecialistOfficeVisit: coverage_copay_type_SpecialistOfficeVisit,
  /**
   * A visit held where the patient is remote relative to the practitioner, e.g. by phone, computer or video conference.
   */
  TeleVisit: coverage_copay_type_TeleVisit,
  /**
   * A visit to an urgent care facility - typically a community care clinic.
   */
  UrgentCare: coverage_copay_type_UrgentCare,
};
const coverageeligibilityresponse_ex_auth_support_AccidentReport: Coding = {
  code: 'accidentreport',
  display: 'Accident Report',
  system: 'http://terminology.hl7.org/CodeSystem/coverageeligibilityresponse-ex-auth-support',
};
const coverageeligibilityresponse_ex_auth_support_DiagnosticImageOrder: Coding = {
  code: 'diagnosticimageorder',
  display: 'Diagnostic Image Order',
  system: 'http://terminology.hl7.org/CodeSystem/coverageeligibilityresponse-ex-auth-support',
};
const coverageeligibilityresponse_ex_auth_support_DiagnosticImageReport: Coding = {
  code: 'diagnosticimagereport',
  display: 'Diagnostic Image Report',
  system: 'http://terminology.hl7.org/CodeSystem/coverageeligibilityresponse-ex-auth-support',
};
const coverageeligibilityresponse_ex_auth_support_LabOrder: Coding = {
  code: 'laborder',
  display: 'Lab Order',
  system: 'http://terminology.hl7.org/CodeSystem/coverageeligibilityresponse-ex-auth-support',
};
const coverageeligibilityresponse_ex_auth_support_LabReport: Coding = {
  code: 'labreport',
  display: 'Lab Report',
  system: 'http://terminology.hl7.org/CodeSystem/coverageeligibilityresponse-ex-auth-support',
};
const coverageeligibilityresponse_ex_auth_support_Model: Coding = {
  code: 'model',
  display: 'Model',
  system: 'http://terminology.hl7.org/CodeSystem/coverageeligibilityresponse-ex-auth-support',
};
const coverageeligibilityresponse_ex_auth_support_Picture: Coding = {
  code: 'picture',
  display: 'Picture',
  system: 'http://terminology.hl7.org/CodeSystem/coverageeligibilityresponse-ex-auth-support',
};
const coverageeligibilityresponse_ex_auth_support_ProfessionalReport: Coding = {
  code: 'professionalreport',
  display: 'Professional Report',
  system: 'http://terminology.hl7.org/CodeSystem/coverageeligibilityresponse-ex-auth-support',
};
/**
 * This value set includes CoverageEligibilityResponse Auth Support codes.
 */
export const CoverageeligibilityresponseExAuthSupport = {
  /**
   * A formal accident report as would be filed with police or a simlar official body.
   */
  AccidentReport: coverageeligibilityresponse_ex_auth_support_AccidentReport,
  /**
   * A request or authorization for diagnostic imaging.
   */
  DiagnosticImageOrder: coverageeligibilityresponse_ex_auth_support_DiagnosticImageOrder,
  /**
   * A report on diagnostic image(s).
   */
  DiagnosticImageReport: coverageeligibilityresponse_ex_auth_support_DiagnosticImageReport,
  /**
   * A request or authorization for laboratory diagnostic tests.
   */
  LabOrder: coverageeligibilityresponse_ex_auth_support_LabOrder,
  /**
   * A report on laboratory diagnostic test(s).
   */
  LabReport: coverageeligibilityresponse_ex_auth_support_LabReport,
  /**
   * A physical model of the affected area.
   */
  Model: coverageeligibilityresponse_ex_auth_support_Model,
  /**
   * A photograph of the affected area.
   */
  Picture: coverageeligibilityresponse_ex_auth_support_Picture,
  /**
   * A report from a licensed professional regarding the siutation, condition or proposed treatment.
   */
  ProfessionalReport: coverageeligibilityresponse_ex_auth_support_ProfessionalReport,
};
const coverage_financial_exception_FosterChild: Coding = {
  code: 'foster',
  display: 'Foster child',
  system: 'http://terminology.hl7.org/CodeSystem/ex-coverage-financial-exception',
};
const coverage_financial_exception_Retired: Coding = {
  code: 'retired',
  display: 'Retired',
  system: 'http://terminology.hl7.org/CodeSystem/ex-coverage-financial-exception',
};
/**
 * This value set includes Example Coverage Financial Exception Codes.
 */
export const CoverageFinancialException = {
  /**
   * Children in the foster care have all copays and deductibles waived.
   */
  FosterChild: coverage_financial_exception_FosterChild,
  /**
   * Retired persons have all copays and deductibles reduced.
   */
  Retired: coverage_financial_exception_Retired,
};
const coverage_selfpay_Pay: Coding = {
  code: 'pay',
  display: 'Pay',
  system: 'http://terminology.hl7.org/CodeSystem/coverage-selfpay',
};
/**
 * This value set includes Coverage SelfPay codes.
 */
export const CoverageSelfpay = {
  /**
   * An individual or organization is paying directly for goods and services.
   */
  Pay: coverage_selfpay_Pay,
};
/**
 * This value set includes Coverage Type codes.
 */
export const CoverageType = {
  /**
   * An individual or organization is paying directly for goods and services.
   */
  Pay: coverage_selfpay_Pay,
};
const data_absent_reason_AskedButDeclined: Coding = {
  code: 'asked-declined',
  display: 'Asked But Declined',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_AskedButUnknown: Coding = {
  code: 'asked-unknown',
  display: 'Asked But Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_AsText: Coding = {
  code: 'as-text',
  display: 'As Text',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_Error: Coding = {
  code: 'error',
  display: 'Error',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_Masked: Coding = {
  code: 'masked',
  display: 'Masked',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_NegativeInfinityNINF: Coding = {
  code: 'negative-infinity',
  display: 'Negative Infinity (NINF)',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_NotANumberNaN: Coding = {
  code: 'not-a-number',
  display: 'Not a Number (NaN)',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_NotApplicable: Coding = {
  code: 'not-applicable',
  display: 'Not Applicable',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_NotAsked: Coding = {
  code: 'not-asked',
  display: 'Not Asked',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_NotPerformed: Coding = {
  code: 'not-performed',
  display: 'Not Performed',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_NotPermitted: Coding = {
  code: 'not-permitted',
  display: 'Not Permitted',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_PositiveInfinityPINF: Coding = {
  code: 'positive-infinity',
  display: 'Positive Infinity (PINF)',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_TemporarilyUnknown: Coding = {
  code: 'temp-unknown',
  display: 'Temporarily Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
const data_absent_reason_Unsupported: Coding = {
  code: 'unsupported',
  display: 'Unsupported',
  system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
};
/**
 * Used to specify why the normally expected content of the data element is missing.
 */
export const DataAbsentReason = {
  /**
   * The source was asked but declined to answer.
   */
  AskedButDeclined: data_absent_reason_AskedButDeclined,
  /**
   * The source was asked but does not know the value.
   */
  AskedButUnknown: data_absent_reason_AskedButUnknown,
  /**
   * The content of the data is represented in the resource narrative.
   */
  AsText: data_absent_reason_AsText,
  /**
   * Some system or workflow process error means that the information is not available.
   */
  Error: data_absent_reason_Error,
  /**
   * The information is not available due to security, privacy or related reasons.
   */
  Masked: data_absent_reason_Masked,
  /**
   * The numeric value is excessively low and unrepresentable due to a floating point processing error.
   */
  NegativeInfinityNINF: data_absent_reason_NegativeInfinityNINF,
  /**
   * The numeric value is undefined or unrepresentable due to a floating point processing error.
   */
  NotANumberNaN: data_absent_reason_NotANumberNaN,
  /**
   * There is no proper value for this element (e.g. last menstrual period for a male).
   */
  NotApplicable: data_absent_reason_NotApplicable,
  /**
   * The workflow didn't lead to this value being known.
   */
  NotAsked: data_absent_reason_NotAsked,
  /**
   * The value is not available because the observation procedure (test, etc.) was not performed.
   */
  NotPerformed: data_absent_reason_NotPerformed,
  /**
   * The value is not permitted in this context (e.g. due to profiles, or the base data types).
   */
  NotPermitted: data_absent_reason_NotPermitted,
  /**
   * The numeric value is excessively high and unrepresentable due to a floating point processing error.
   */
  PositiveInfinityPINF: data_absent_reason_PositiveInfinityPINF,
  /**
   * There is reason to expect (from the workflow) that the value may become known.
   */
  TemporarilyUnknown: data_absent_reason_TemporarilyUnknown,
  /**
   * The value is expected to exist but is not known.
   */
  Unknown: data_absent_reason_Unknown,
  /**
   * The source system wasn't capable of supporting this element.
   */
  Unsupported: data_absent_reason_Unsupported,
};
/**
 * A version specific list of the data types defined by the FHIR specification for use as an element  type (any of the FHIR defined data types).
 */
export const DataTypes = {
  /**
   * An address expressed using postal conventions (as opposed to GPS or other location definition formats).  This data type may be used to convey addresses for use in delivering mail as well as for visiting locations which might not be valid for mail delivery.  There are a variety of postal address formats defined around the world.
   */
  Address: data_types_Address,
  /**
   * A duration of time during which an organism (or a process) has existed.
   */
  Age: data_types_Age,
  /**
   * A  text note which also  contains information about who made the statement and when.
   */
  Annotation: data_types_Annotation,
  /**
   * For referring to data content defined in other formats.
   */
  Attachment: data_types_Attachment,
  /**
   * Base definition for all elements that are defined inside a resource - but not those in a data type.
   */
  BackboneElement: data_types_BackboneElement,
  /**
   * A stream of bytes
   */
  Base64Binary: data_types_Base64Binary,
  /**
   * Value of "true" or "false"
   */
  Boolean: data_types_Boolean,
  /**
   * A URI that is a reference to a canonical URL on a FHIR resource
   */
  Canonical: data_types_Canonical,
  /**
   * A string which has at least one character and no leading or trailing whitespace and where there is no whitespace other than single spaces in the contents
   */
  Code: data_types_Code,
  /**
   * A concept that may be defined by a formal reference to a terminology or ontology or may be provided by text.
   */
  CodeableConcept: data_types_CodeableConcept,
  /**
   * A reference to a code defined by a terminology system.
   */
  Coding: data_types_Coding,
  /**
   * Specifies contact information for a person or organization.
   */
  ContactDetail: data_types_ContactDetail,
  /**
   * Details for all kinds of technology mediated contact points for a person or organization, including telephone, email, etc.
   */
  ContactPoint: data_types_ContactPoint,
  /**
   * A contributor to the content of a knowledge asset, including authors, editors, reviewers, and endorsers.
   */
  Contributor: data_types_Contributor,
  /**
   * A measured amount (or an amount that can potentially be measured). Note that measured amounts include amounts that are not precisely quantified, including amounts involving arbitrary units and floating currencies.
   */
  Count: data_types_Count,
  /**
   * Describes a required data item for evaluation in terms of the type of data, and optional code or date-based filters of the data.
   */
  DataRequirement: data_types_DataRequirement,
  /**
   * A date or partial date (e.g. just year or year + month). There is no time zone. The format is a union of the schema types gYear, gYearMonth and date.  Dates SHALL be valid dates.
   */
  Date: data_types_Date,
  /**
   * A date, date-time or partial date (e.g. just year or year + month).  If hours and minutes are specified, a time zone SHALL be populated. The format is a union of the schema types gYear, gYearMonth, date and dateTime. Seconds must be provided due to schema type constraints but may be zero-filled and may be ignored.                 Dates SHALL be valid dates.
   */
  DateTime: data_types_DateTime,
  /**
   * A rational number with implicit precision
   */
  Decimal: data_types_Decimal,
  /**
   * A length - a value with a unit that is a physical distance.
   */
  Distance: data_types_Distance,
  /**
   * Indicates how the medication is/was taken or should be taken by the patient.
   */
  Dosage: data_types_Dosage,
  /**
   * A length of time.
   */
  Duration: data_types_Duration,
  /**
   * Base definition for all elements in a resource.
   */
  Element: data_types_Element,
  /**
   * Captures constraints on each element within the resource, profile, or extension.
   */
  ElementDefinition: data_types_ElementDefinition,
  /**
   * A expression that is evaluated in a specified context and returns a value. The context of use of the expression must specify the context in which the expression is evaluated, and how the result of the expression is used.
   */
  Expression: data_types_Expression,
  /**
   * Optional Extension Element - found in all resources.
   */
  Extension: data_types_Extension,
  /**
   * A human's name with the ability to identify parts and usage.
   */
  HumanName: data_types_HumanName,
  /**
   * Any combination of letters, numerals, "-" and ".", with a length limit of 64 characters.  (This might be an integer, an unprefixed OID, UUID or any other identifier pattern that meets these constraints.)  Ids are case-insensitive.
   */
  Id: data_types_Id,
  /**
   * An identifier - identifies some entity uniquely and unambiguously. Typically this is used for business identifiers.
   */
  Identifier: data_types_Identifier,
  /**
   * An instant in time - known at least to the second
   */
  Instant: data_types_Instant,
  /**
   * A whole number
   */
  Integer: data_types_Integer,
  /**
   * A string that may contain Github Flavored Markdown syntax for optional processing by a mark down presentation engine
   */
  Markdown: data_types_Markdown,
  /**
   * The marketing status describes the date when a medicinal product is actually put on the market or the date as of which it is no longer available.
   */
  MarketingStatus: data_types_MarketingStatus,
  /**
   * The metadata about a resource. This is content in the resource that is maintained by the infrastructure. Changes to the content might not always be associated with version changes to the resource.
   */
  Meta: data_types_Meta,
  /**
   * An amount of economic utility in some recognized currency.
   */
  Money: data_types_Money,
  MoneyQuantity: data_types_MoneyQuantity,
  /**
   * A human-readable summary of the resource conveying the essential clinical and business information for the resource.
   */
  Narrative: data_types_Narrative,
  /**
   * An OID represented as a URI
   */
  Oid: data_types_Oid,
  /**
   * The parameters to the module. This collection specifies both the input and output parameters. Input parameters are provided by the caller as part of the $evaluate operation. Output parameters are included in the GuidanceResponse.
   */
  ParameterDefinition: data_types_ParameterDefinition,
  /**
   * A time period defined by a start and end date and optionally time.
   */
  Period: data_types_Period,
  /**
   * A populatioof people with some set of grouping criteria.
   */
  Population: data_types_Population,
  /**
   * An integer with a value that is positive (e.g. >0)
   */
  PositiveInt: data_types_PositiveInt,
  /**
   * The marketing status describes the date when a medicinal product is actually put on the market or the date as of which it is no longer available.
   */
  ProdCharacteristic: data_types_ProdCharacteristic,
  /**
   * The shelf-life and storage information for a medicinal product item or container can be described using this class.
   */
  ProductShelfLife: data_types_ProductShelfLife,
  /**
   * A measured amount (or an amount that can potentially be measured). Note that measured amounts include amounts that are not precisely quantified, including amounts involving arbitrary units and floating currencies.
   */
  Quantity: data_types_Quantity,
  /**
   * A set of ordered Quantities defined by a low and high limit.
   */
  Range: data_types_Range,
  /**
   * A relationship of two Quantity values - expressed as a numerator and a denominator.
   */
  Ratio: data_types_Ratio,
  /**
   * A reference from one resource to another.
   */
  Reference: data_types_Reference,
  /**
   * Related artifacts such as additional documentation, justification, or bibliographic references.
   */
  RelatedArtifact: data_types_RelatedArtifact,
  /**
   * A series of measurements taken by a device, with upper and lower limits. There may be more than one dimension in the data.
   */
  SampledData: data_types_SampledData,
  /**
   * A signature along with supporting context. The signature may be a digital signature that is cryptographic in nature, or some other signature acceptable to the domain. This other signature may be as simple as a graphical image representing a hand-written signature, or a signature ceremony Different signature approaches have different utilities.
   */
  Signature: data_types_Signature,
  SimpleQuantity: data_types_SimpleQuantity,
  /**
   * A sequence of Unicode characters
   */
  String: data_types_String,
  /**
   * Chemical substances are a single substance type whose primary defining element is the molecular structure. Chemical substances shall be defined on the basis of their complete covalent molecular structure; the presence of a salt (counter-ion) and/or solvates (water, alcohols) is also captured. Purity, grade, physical form or particle size are not taken into account in the definition of a chemical substance or in the assignment of a Substance ID.
   */
  SubstanceAmount: data_types_SubstanceAmount,
  /**
   * A time during the day, with no date specified
   */
  Time: data_types_Time,
  /**
   * Specifies an event that may occur multiple times. Timing schedules are used to record when things are planned, expected or requested to occur. The most common usage is in dosage instructions for medications. They are also used when planning care of various kinds, and may be used for reporting the schedule to which past regular activities were carried out.
   */
  Timing: data_types_Timing,
  /**
   * A description of a triggering event. Triggering events can be named events, data events, or periodic, as determined by the type element.
   */
  TriggerDefinition: data_types_TriggerDefinition,
  /**
   * An integer with a value that is not negative (e.g. >= 0)
   */
  UnsignedInt: data_types_UnsignedInt,
  /**
   * String of characters used to identify a name or a resource
   */
  Uri: data_types_Uri,
  /**
   * A URI that is a literal reference
   */
  Url: data_types_Url,
  /**
   * Specifies clinical/business/etc. metadata that can be used to retrieve, index and/or categorize an artifact. This metadata can either be specific to the applicable population (e.g., age category, DRG) or the specific context of care (e.g., venue, care setting, provider of care).
   */
  UsageContext: data_types_UsageContext,
  /**
   * A UUID, represented as a URI
   */
  Uuid: data_types_Uuid,
  /**
   * XHTML format, as defined by W3C, but restricted usage (mainly, no active content)
   */
  XHTML: data_types_XHTML,
};
const days_of_week_Friday: Coding = {
  code: 'fri',
  display: 'Friday',
  system: 'http://hl7.org/fhir/days-of-week',
};
const days_of_week_Monday: Coding = {
  code: 'mon',
  display: 'Monday',
  system: 'http://hl7.org/fhir/days-of-week',
};
const days_of_week_Saturday: Coding = {
  code: 'sat',
  display: 'Saturday',
  system: 'http://hl7.org/fhir/days-of-week',
};
const days_of_week_Sunday: Coding = {
  code: 'sun',
  display: 'Sunday',
  system: 'http://hl7.org/fhir/days-of-week',
};
const days_of_week_Thursday: Coding = {
  code: 'thu',
  display: 'Thursday',
  system: 'http://hl7.org/fhir/days-of-week',
};
const days_of_week_Tuesday: Coding = {
  code: 'tue',
  display: 'Tuesday',
  system: 'http://hl7.org/fhir/days-of-week',
};
const days_of_week_Wednesday: Coding = {
  code: 'wed',
  display: 'Wednesday',
  system: 'http://hl7.org/fhir/days-of-week',
};
/**
 * The days of the week.
 */
export const DaysOfWeek = {
  /**
   * Friday.
   */
  Friday: days_of_week_Friday,
  /**
   * Monday.
   */
  Monday: days_of_week_Monday,
  /**
   * Saturday.
   */
  Saturday: days_of_week_Saturday,
  /**
   * Sunday.
   */
  Sunday: days_of_week_Sunday,
  /**
   * Thursday.
   */
  Thursday: days_of_week_Thursday,
  /**
   * Tuesday.
   */
  Tuesday: days_of_week_Tuesday,
  /**
   * Wednesday.
   */
  Wednesday: days_of_week_Wednesday,
};
/**
 * A list of all the concrete types defined in this version of the FHIR specification - Data Types and Resource Types.
 */
export const DefinedTypes = {
  /**
   * A financial tool for tracking value accrued for a particular purpose.  In the healthcare field, used to track charges for a patient, cost centers, etc.
   */
  Account_resource_types: resource_types_Account,
  /**
   * This resource allows for the definition of some activity to be performed, independent of a particular patient, practitioner, or other performance context.
   */
  ActivityDefinition_resource_types: resource_types_ActivityDefinition,
  /**
   * An address expressed using postal conventions (as opposed to GPS or other location definition formats).  This data type may be used to convey addresses for use in delivering mail as well as for visiting locations which might not be valid for mail delivery.  There are a variety of postal address formats defined around the world.
   */
  Address_data_types: data_types_Address,
  /**
   * Actual or  potential/avoided event causing unintended physical injury resulting from or contributed to by medical care, a research study or other healthcare setting factors that requires additional monitoring, treatment, or hospitalization, or that results in death.
   */
  AdverseEvent_resource_types: resource_types_AdverseEvent,
  /**
   * A duration of time during which an organism (or a process) has existed.
   */
  Age_data_types: data_types_Age,
  /**
   * Risk of harmful or undesirable, physiological response which is unique to an individual and associated with exposure to a substance.
   */
  AllergyIntolerance_resource_types: resource_types_AllergyIntolerance,
  /**
   * A  text note which also  contains information about who made the statement and when.
   */
  Annotation_data_types: data_types_Annotation,
  /**
   * A booking of a healthcare event among patient(s), practitioner(s), related person(s) and/or device(s) for a specific date/time. This may result in one or more Encounter(s).
   */
  Appointment_resource_types: resource_types_Appointment,
  /**
   * A reply to an appointment request for a patient and/or practitioner(s), such as a confirmation or rejection.
   */
  AppointmentResponse_resource_types: resource_types_AppointmentResponse,
  /**
   * For referring to data content defined in other formats.
   */
  Attachment_data_types: data_types_Attachment,
  /**
   * A record of an event made for purposes of maintaining a security log. Typical uses include detection of intrusion attempts and monitoring for inappropriate usage.
   */
  AuditEvent_resource_types: resource_types_AuditEvent,
  /**
   * Base definition for all elements that are defined inside a resource - but not those in a data type.
   */
  BackboneElement_data_types: data_types_BackboneElement,
  /**
   * A stream of bytes
   */
  Base64Binary_data_types: data_types_Base64Binary,
  /**
   * Basic is used for handling concepts not yet defined in FHIR, narrative-only resources that don't map to an existing resource, and custom resources not appropriate for inclusion in the FHIR specification.
   */
  Basic_resource_types: resource_types_Basic,
  /**
   * A resource that represents the data of a single raw artifact as digital content accessible in its native format.  A Binary resource can contain any content, whether text, image, pdf, zip archive, etc.
   */
  Binary_resource_types: resource_types_Binary,
  /**
   * A material substance originating from a biological entity intended to be transplanted or infused
   * into another (possibly the same) biological entity.
   */
  BiologicallyDerivedProduct_resource_types: resource_types_BiologicallyDerivedProduct,
  /**
   * Record details about an anatomical structure.  This resource may be used when a coded concept does not provide the necessary detail needed for the use case.
   */
  BodyStructure_resource_types: resource_types_BodyStructure,
  /**
   * Value of "true" or "false"
   */
  Boolean_data_types: data_types_Boolean,
  /**
   * A container for a collection of resources.
   */
  Bundle_resource_types: resource_types_Bundle,
  /**
   * A URI that is a reference to a canonical URL on a FHIR resource
   */
  Canonical_data_types: data_types_Canonical,
  /**
   * A Capability Statement documents a set of capabilities (behaviors) of a FHIR Server for a particular version of FHIR that may be used as a statement of actual server functionality or a statement of required or desired server implementation.
   */
  CapabilityStatement_resource_types: resource_types_CapabilityStatement,
  /**
   * Describes the intention of how one or more practitioners intend to deliver care for a particular patient, group or community for a period of time, possibly limited to care for a specific condition or set of conditions.
   */
  CarePlan_resource_types: resource_types_CarePlan,
  /**
   * The Care Team includes all the people and organizations who plan to participate in the coordination and delivery of care for a patient.
   */
  CareTeam_resource_types: resource_types_CareTeam,
  /**
   * Catalog entries are wrappers that contextualize items included in a catalog.
   */
  CatalogEntry_resource_types: resource_types_CatalogEntry,
  /**
   * The resource ChargeItem describes the provision of healthcare provider products for a certain patient, therefore referring not only to the product, but containing in addition details of the provision, like date, time, amounts and participating organizations and persons. Main Usage of the ChargeItem is to enable the billing process and internal cost allocation.
   */
  ChargeItem_resource_types: resource_types_ChargeItem,
  /**
   * The ChargeItemDefinition resource provides the properties that apply to the (billing) codes necessary to calculate costs and prices. The properties may differ largely depending on type and realm, therefore this resource gives only a rough structure and requires profiling for each type of billing code system.
   */
  ChargeItemDefinition_resource_types: resource_types_ChargeItemDefinition,
  /**
   * A provider issued list of professional services and products which have been provided, or are to be provided, to a patient which is sent to an insurer for reimbursement.
   */
  Claim_resource_types: resource_types_Claim,
  /**
   * This resource provides the adjudication details from the processing of a Claim resource.
   */
  ClaimResponse_resource_types: resource_types_ClaimResponse,
  /**
   * A record of a clinical assessment performed to determine what problem(s) may affect the patient and before planning the treatments or management strategies that are best to manage a patient's condition. Assessments are often 1:1 with a clinical consultation / encounter,  but this varies greatly depending on the clinical workflow. This resource is called "ClinicalImpression" rather than "ClinicalAssessment" to avoid confusion with the recording of assessment tools such as Apgar score.
   */
  ClinicalImpression_resource_types: resource_types_ClinicalImpression,
  /**
   * A string which has at least one character and no leading or trailing whitespace and where there is no whitespace other than single spaces in the contents
   */
  Code_data_types: data_types_Code,
  /**
   * A concept that may be defined by a formal reference to a terminology or ontology or may be provided by text.
   */
  CodeableConcept_data_types: data_types_CodeableConcept,
  /**
   * The CodeSystem resource is used to declare the existence of and describe a code system or code system supplement and its key properties, and optionally define a part or all of its content.
   */
  CodeSystem_resource_types: resource_types_CodeSystem,
  /**
   * A reference to a code defined by a terminology system.
   */
  Coding_data_types: data_types_Coding,
  /**
   * An occurrence of information being transmitted; e.g. an alert that was sent to a responsible provider, a public health agency that was notified about a reportable condition.
   */
  Communication_resource_types: resource_types_Communication,
  /**
   * A request to convey information; e.g. the CDS system proposes that an alert be sent to a responsible provider, the CDS system proposes that the public health agency be notified about a reportable condition.
   */
  CommunicationRequest_resource_types: resource_types_CommunicationRequest,
  /**
   * A compartment definition that defines how resources are accessed on a server.
   */
  CompartmentDefinition_resource_types: resource_types_CompartmentDefinition,
  /**
   * A set of healthcare-related information that is assembled together into a single logical package that provides a single coherent statement of meaning, establishes its own context and that has clinical attestation with regard to who is making the statement. A Composition defines the structure and narrative content necessary for a document. However, a Composition alone does not constitute a document. Rather, the Composition must be the first entry in a Bundle where Bundle.type=document, and any other resources referenced from Composition must be included as subsequent entries in the Bundle (for example Patient, Practitioner, Encounter, etc.).
   */
  Composition_resource_types: resource_types_Composition,
  /**
   * A statement of relationships from one set of concepts to one or more other concepts - either concepts in code systems, or data element/data element concepts, or classes in class models.
   */
  ConceptMap_resource_types: resource_types_ConceptMap,
  /**
   * A clinical condition, problem, diagnosis, or other event, situation, issue, or clinical concept that has risen to a level of concern.
   */
  Condition_resource_types: resource_types_Condition,
  /**
   * A record of a healthcare consumer’s  choices, which permits or denies identified recipient(s) or recipient role(s) to perform one or more actions within a given policy context, for specific purposes and periods of time.
   */
  Consent_resource_types: resource_types_Consent,
  /**
   * Specifies contact information for a person or organization.
   */
  ContactDetail_data_types: data_types_ContactDetail,
  /**
   * Details for all kinds of technology mediated contact points for a person or organization, including telephone, email, etc.
   */
  ContactPoint_data_types: data_types_ContactPoint,
  /**
   * Legally enforceable, formally recorded unilateral or bilateral directive i.e., a policy or agreement.
   */
  Contract_resource_types: resource_types_Contract,
  /**
   * A contributor to the content of a knowledge asset, including authors, editors, reviewers, and endorsers.
   */
  Contributor_data_types: data_types_Contributor,
  /**
   * A measured amount (or an amount that can potentially be measured). Note that measured amounts include amounts that are not precisely quantified, including amounts involving arbitrary units and floating currencies.
   */
  Count_data_types: data_types_Count,
  /**
   * Financial instrument which may be used to reimburse or pay for health care products and services. Includes both insurance and self-payment.
   */
  Coverage_resource_types: resource_types_Coverage,
  /**
   * The CoverageEligibilityRequest provides patient and insurance coverage information to an insurer for them to respond, in the form of an CoverageEligibilityResponse, with information regarding whether the stated coverage is valid and in-force and optionally to provide the insurance details of the policy.
   */
  CoverageEligibilityRequest_resource_types: resource_types_CoverageEligibilityRequest,
  /**
   * This resource provides eligibility and plan details from the processing of an CoverageEligibilityRequest resource.
   */
  CoverageEligibilityResponse_resource_types: resource_types_CoverageEligibilityResponse,
  /**
   * Describes a required data item for evaluation in terms of the type of data, and optional code or date-based filters of the data.
   */
  DataRequirement_data_types: data_types_DataRequirement,
  /**
   * A date or partial date (e.g. just year or year + month). There is no time zone. The format is a union of the schema types gYear, gYearMonth and date.  Dates SHALL be valid dates.
   */
  Date_data_types: data_types_Date,
  /**
   * A date, date-time or partial date (e.g. just year or year + month).  If hours and minutes are specified, a time zone SHALL be populated. The format is a union of the schema types gYear, gYearMonth, date and dateTime. Seconds must be provided due to schema type constraints but may be zero-filled and may be ignored.                 Dates SHALL be valid dates.
   */
  DateTime_data_types: data_types_DateTime,
  /**
   * A rational number with implicit precision
   */
  Decimal_data_types: data_types_Decimal,
  /**
   * Indicates an actual or potential clinical issue with or between one or more active or proposed clinical actions for a patient; e.g. Drug-drug interaction, Ineffective treatment frequency, Procedure-condition conflict, etc.
   */
  DetectedIssue_resource_types: resource_types_DetectedIssue,
  /**
   * A type of a manufactured item that is used in the provision of healthcare without being substantially changed through that activity. The device may be a medical or non-medical device.
   */
  Device_resource_types: resource_types_Device,
  /**
   * The characteristics, operational status and capabilities of a medical-related component of a medical device.
   */
  DeviceDefinition_resource_types: resource_types_DeviceDefinition,
  /**
   * Describes a measurement, calculation or setting capability of a medical device.
   */
  DeviceMetric_resource_types: resource_types_DeviceMetric,
  /**
   * Represents a request for a patient to employ a medical device. The device may be an implantable device, or an external assistive device, such as a walker.
   */
  DeviceRequest_resource_types: resource_types_DeviceRequest,
  /**
   * A record of a device being used by a patient where the record is the result of a report from the patient or another clinician.
   */
  DeviceUseStatement_resource_types: resource_types_DeviceUseStatement,
  /**
   * The findings and interpretation of diagnostic  tests performed on patients, groups of patients, devices, and locations, and/or specimens derived from these. The report includes clinical context such as requesting and provider information, and some mix of atomic results, images, textual and coded interpretations, and formatted representation of diagnostic reports.
   */
  DiagnosticReport_resource_types: resource_types_DiagnosticReport,
  /**
   * A length - a value with a unit that is a physical distance.
   */
  Distance_data_types: data_types_Distance,
  /**
   * A collection of documents compiled for a purpose together with metadata that applies to the collection.
   */
  DocumentManifest_resource_types: resource_types_DocumentManifest,
  /**
   * A reference to a document of any kind for any purpose. Provides metadata about the document so that the document can be discovered and managed. The scope of a document is any seralized object with a mime-type, so includes formal patient centric documents (CDA), cliical notes, scanned paper, and non-patient specific documents like policy text.
   */
  DocumentReference_resource_types: resource_types_DocumentReference,
  /**
   * A resource that includes narrative, extensions, and contained resources.
   */
  DomainResource_resource_types: resource_types_DomainResource,
  /**
   * Indicates how the medication is/was taken or should be taken by the patient.
   */
  Dosage_data_types: data_types_Dosage,
  /**
   * A length of time.
   */
  Duration_data_types: data_types_Duration,
  /**
   * The EffectEvidenceSynthesis resource describes the difference in an outcome between exposures states in a population where the effect estimate is derived from a combination of research studies.
   */
  EffectEvidenceSynthesis_resource_types: resource_types_EffectEvidenceSynthesis,
  /**
   * Base definition for all elements in a resource.
   */
  Element_data_types: data_types_Element,
  /**
   * Captures constraints on each element within the resource, profile, or extension.
   */
  ElementDefinition_data_types: data_types_ElementDefinition,
  /**
   * An interaction between a patient and healthcare provider(s) for the purpose of providing healthcare service(s) or assessing the health status of a patient.
   */
  Encounter_resource_types: resource_types_Encounter,
  /**
   * The technical details of an endpoint that can be used for electronic services, such as for web services providing XDS.b or a REST endpoint for another FHIR server. This may include any security context information.
   */
  Endpoint_resource_types: resource_types_Endpoint,
  /**
   * This resource provides the insurance enrollment details to the insurer regarding a specified coverage.
   */
  EnrollmentRequest_resource_types: resource_types_EnrollmentRequest,
  /**
   * This resource provides enrollment and plan details from the processing of an EnrollmentRequest resource.
   */
  EnrollmentResponse_resource_types: resource_types_EnrollmentResponse,
  /**
   * An association between a patient and an organization / healthcare provider(s) during which time encounters may occur. The managing organization assumes a level of responsibility for the patient during this time.
   */
  EpisodeOfCare_resource_types: resource_types_EpisodeOfCare,
  /**
   * The EventDefinition resource provides a reusable description of when a particular event can occur.
   */
  EventDefinition_resource_types: resource_types_EventDefinition,
  /**
   * The Evidence resource describes the conditional state (population and any exposures being compared within the population) and outcome (if specified) that the knowledge (evidence, assertion, recommendation) is about.
   */
  Evidence_resource_types: resource_types_Evidence,
  /**
   * The EvidenceVariable resource describes a "PICO" element that knowledge (evidence, assertion, recommendation) is about.
   */
  EvidenceVariable_resource_types: resource_types_EvidenceVariable,
  /**
   * Example of workflow instance.
   */
  ExampleScenario_resource_types: resource_types_ExampleScenario,
  /**
   * This resource provides: the claim details; adjudication details from the processing of a Claim; and optionally account balance information, for informing the subscriber of the benefits provided.
   */
  ExplanationOfBenefit_resource_types: resource_types_ExplanationOfBenefit,
  /**
   * A expression that is evaluated in a specified context and returns a value. The context of use of the expression must specify the context in which the expression is evaluated, and how the result of the expression is used.
   */
  Expression_data_types: data_types_Expression,
  /**
   * Optional Extension Element - found in all resources.
   */
  Extension_data_types: data_types_Extension,
  /**
   * Significant health conditions for a person related to the patient relevant in the context of care for the patient.
   */
  FamilyMemberHistory_resource_types: resource_types_FamilyMemberHistory,
  /**
   * Prospective warnings of potential issues when providing care to the patient.
   */
  Flag_resource_types: resource_types_Flag,
  /**
   * Describes the intended objective(s) for a patient, group or organization care, for example, weight loss, restoring an activity of daily living, obtaining herd immunity via immunization, meeting a process improvement objective, etc.
   */
  Goal_resource_types: resource_types_Goal,
  /**
   * A formal computable definition of a graph of resources - that is, a coherent set of resources that form a graph by following references. The Graph Definition resource defines a set and makes rules about the set.
   */
  GraphDefinition_resource_types: resource_types_GraphDefinition,
  /**
   * Represents a defined collection of entities that may be discussed or acted upon collectively but which are not expected to act collectively, and are not formally or legally recognized; i.e. a collection of entities that isn't an Organization.
   */
  Group_resource_types: resource_types_Group,
  /**
   * A guidance response is the formal response to a guidance request, including any output parameters returned by the evaluation, as well as the description of any proposed actions to be taken.
   */
  GuidanceResponse_resource_types: resource_types_GuidanceResponse,
  /**
   * The details of a healthcare service available at a location.
   */
  HealthcareService_resource_types: resource_types_HealthcareService,
  /**
   * A human's name with the ability to identify parts and usage.
   */
  HumanName_data_types: data_types_HumanName,
  /**
   * Any combination of letters, numerals, "-" and ".", with a length limit of 64 characters.  (This might be an integer, an unprefixed OID, UUID or any other identifier pattern that meets these constraints.)  Ids are case-insensitive.
   */
  Id_data_types: data_types_Id,
  /**
   * An identifier - identifies some entity uniquely and unambiguously. Typically this is used for business identifiers.
   */
  Identifier_data_types: data_types_Identifier,
  /**
   * Representation of the content produced in a DICOM imaging study. A study comprises a set of series, each of which includes a set of Service-Object Pair Instances (SOP Instances - images or other data) acquired or produced in a common context.  A series is of only one modality (e.g. X-ray, CT, MR, ultrasound), but a study may have multiple series of different modalities.
   */
  ImagingStudy_resource_types: resource_types_ImagingStudy,
  /**
   * Describes the event of a patient being administered a vaccine or a record of an immunization as reported by a patient, a clinician or another party.
   */
  Immunization_resource_types: resource_types_Immunization,
  /**
   * Describes a comparison of an immunization event against published recommendations to determine if the administration is "valid" in relation to those  recommendations.
   */
  ImmunizationEvaluation_resource_types: resource_types_ImmunizationEvaluation,
  /**
   * A patient's point-in-time set of recommendations (i.e. forecasting) according to a published schedule with optional supporting justification.
   */
  ImmunizationRecommendation_resource_types: resource_types_ImmunizationRecommendation,
  /**
   * A set of rules of how a particular interoperability or standards problem is solved - typically through the use of FHIR resources. This resource is used to gather all the parts of an implementation guide into a logical whole and to publish a computable definition of all the parts.
   */
  ImplementationGuide_resource_types: resource_types_ImplementationGuide,
  /**
   * An instant in time - known at least to the second
   */
  Instant_data_types: data_types_Instant,
  /**
   * Details of a Health Insurance product/plan provided by an organization.
   */
  InsurancePlan_resource_types: resource_types_InsurancePlan,
  /**
   * A whole number
   */
  Integer_data_types: data_types_Integer,
  /**
   * Invoice containing collected ChargeItems from an Account with calculated individual and total price for Billing purpose.
   */
  Invoice_resource_types: resource_types_Invoice,
  /**
   * The Library resource is a general-purpose container for knowledge asset definitions. It can be used to describe and expose existing knowledge assets such as logic libraries and information model descriptions, as well as to describe a collection of knowledge assets.
   */
  Library_resource_types: resource_types_Library,
  /**
   * Identifies two or more records (resource instances) that refer to the same real-world "occurrence".
   */
  Linkage_resource_types: resource_types_Linkage,
  /**
   * A list is a curated collection of resources.
   */
  List_resource_types: resource_types_List,
  /**
   * Details and position information for a physical place where services are provided and resources and participants may be stored, found, contained, or accommodated.
   */
  Location_resource_types: resource_types_Location,
  /**
   * A string that may contain Github Flavored Markdown syntax for optional processing by a mark down presentation engine
   */
  Markdown_data_types: data_types_Markdown,
  /**
   * The marketing status describes the date when a medicinal product is actually put on the market or the date as of which it is no longer available.
   */
  MarketingStatus_data_types: data_types_MarketingStatus,
  /**
   * The Measure resource provides the definition of a quality measure.
   */
  Measure_resource_types: resource_types_Measure,
  /**
   * The MeasureReport resource contains the results of the calculation of a measure; and optionally a reference to the resources involved in that calculation.
   */
  MeasureReport_resource_types: resource_types_MeasureReport,
  /**
   * A photo, video, or audio recording acquired or used in healthcare. The actual content may be inline or provided by direct reference.
   */
  Media_resource_types: resource_types_Media,
  /**
   * This resource is primarily used for the identification and definition of a medication for the purposes of prescribing, dispensing, and administering a medication as well as for making statements about medication use.
   */
  Medication_resource_types: resource_types_Medication,
  /**
   * Describes the event of a patient consuming or otherwise being administered a medication.  This may be as simple as swallowing a tablet or it may be a long running infusion.  Related resources tie this event to the authorizing prescription, and the specific encounter between patient and health care practitioner.
   */
  MedicationAdministration_resource_types: resource_types_MedicationAdministration,
  /**
   * Indicates that a medication product is to be or has been dispensed for a named person/patient.  This includes a description of the medication product (supply) provided and the instructions for administering the medication.  The medication dispense is the result of a pharmacy system responding to a medication order.
   */
  MedicationDispense_resource_types: resource_types_MedicationDispense,
  /**
   * Information about a medication that is used to support knowledge.
   */
  MedicationKnowledge_resource_types: resource_types_MedicationKnowledge,
  /**
   * An order or request for both supply of the medication and the instructions for administration of the medication to a patient. The resource is called "MedicationRequest" rather than "MedicationPrescription" or "MedicationOrder" to generalize the use across inpatient and outpatient settings, including care plans, etc., and to harmonize with workflow patterns.
   */
  MedicationRequest_resource_types: resource_types_MedicationRequest,
  /**
   * A record of a medication that is being consumed by a patient.   A MedicationStatement may indicate that the patient may be taking the medication now or has taken the medication in the past or will be taking the medication in the future.  The source of this information can be the patient, significant other (such as a family member or spouse), or a clinician.  A common scenario where this information is captured is during the history taking process during a patient visit or stay.   The medication information may come from sources such as the patient's memory, from a prescription bottle,  or from a list of medications the patient, clinician or other party maintains.
   * The primary difference between a medication statement and a medication administration is that the medication administration has complete administration information and is based on actual administration information from the person who administered the medication.  A medication statement is often, if not always, less specific.  There is no required date/time when the medication was administered, in fact we only know that a source has reported the patient is taking this medication, where details such as time, quantity, or rate or even medication product may be incomplete or missing or less precise.  As stated earlier, the medication statement information may come from the patient's memory, from a prescription bottle or from a list of medications the patient, clinician or other party maintains.  Medication administration is more formal and is not missing detailed information.
   */
  MedicationStatement_resource_types: resource_types_MedicationStatement,
  /**
   * Detailed definition of a medicinal product, typically for uses other than direct patient care (e.g. regulatory use).
   */
  MedicinalProduct_resource_types: resource_types_MedicinalProduct,
  /**
   * The regulatory authorization of a medicinal product.
   */
  MedicinalProductAuthorization_resource_types: resource_types_MedicinalProductAuthorization,
  /**
   * The clinical particulars - indications, contraindications etc. of a medicinal product, including for regulatory purposes.
   */
  MedicinalProductContraindication_resource_types: resource_types_MedicinalProductContraindication,
  /**
   * Indication for the Medicinal Product.
   */
  MedicinalProductIndication_resource_types: resource_types_MedicinalProductIndication,
  /**
   * An ingredient of a manufactured item or pharmaceutical product.
   */
  MedicinalProductIngredient_resource_types: resource_types_MedicinalProductIngredient,
  /**
   * The interactions of the medicinal product with other medicinal products, or other forms of interactions.
   */
  MedicinalProductInteraction_resource_types: resource_types_MedicinalProductInteraction,
  /**
   * The manufactured item as contained in the packaged medicinal product.
   */
  MedicinalProductManufactured_resource_types: resource_types_MedicinalProductManufactured,
  /**
   * A medicinal product in a container or package.
   */
  MedicinalProductPackaged_resource_types: resource_types_MedicinalProductPackaged,
  /**
   * A pharmaceutical product described in terms of its composition and dose form.
   */
  MedicinalProductPharmaceutical_resource_types: resource_types_MedicinalProductPharmaceutical,
  /**
   * Describe the undesirable effects of the medicinal product.
   */
  MedicinalProductUndesirableEffect_resource_types: resource_types_MedicinalProductUndesirableEffect,
  /**
   * Defines the characteristics of a message that can be shared between systems, including the type of event that initiates the message, the content to be transmitted and what response(s), if any, are permitted.
   */
  MessageDefinition_resource_types: resource_types_MessageDefinition,
  /**
   * The header for a message exchange that is either requesting or responding to an action.  The reference(s) that are the subject of the action as well as other information related to the action are typically transmitted in a bundle in which the MessageHeader resource instance is the first resource in the bundle.
   */
  MessageHeader_resource_types: resource_types_MessageHeader,
  /**
   * The metadata about a resource. This is content in the resource that is maintained by the infrastructure. Changes to the content might not always be associated with version changes to the resource.
   */
  Meta_data_types: data_types_Meta,
  /**
   * Raw data describing a biological sequence.
   */
  MolecularSequence_resource_types: resource_types_MolecularSequence,
  /**
   * An amount of economic utility in some recognized currency.
   */
  Money_data_types: data_types_Money,
  MoneyQuantity_data_types: data_types_MoneyQuantity,
  /**
   * A curated namespace that issues unique symbols within that namespace for the identification of concepts, people, devices, etc.  Represents a "System" used within the Identifier and Coding data types.
   */
  NamingSystem_resource_types: resource_types_NamingSystem,
  /**
   * A human-readable summary of the resource conveying the essential clinical and business information for the resource.
   */
  Narrative_data_types: data_types_Narrative,
  /**
   * A request to supply a diet, formula feeding (enteral) or oral nutritional supplement to a patient/resident.
   */
  NutritionOrder_resource_types: resource_types_NutritionOrder,
  /**
   * Measurements and simple assertions made about a patient, device or other subject.
   */
  Observation_resource_types: resource_types_Observation,
  /**
   * Set of definitional characteristics for a kind of observation or measurement produced or consumed by an orderable health care service.
   */
  ObservationDefinition_resource_types: resource_types_ObservationDefinition,
  /**
   * An OID represented as a URI
   */
  Oid_data_types: data_types_Oid,
  /**
   * A formal computable definition of an operation (on the RESTful interface) or a named query (using the search interaction).
   */
  OperationDefinition_resource_types: resource_types_OperationDefinition,
  /**
   * A collection of error, warning, or information messages that result from a system action.
   */
  OperationOutcome_resource_types: resource_types_OperationOutcome,
  /**
   * A formally or informally recognized grouping of people or organizations formed for the purpose of achieving some form of collective action.  Includes companies, institutions, corporations, departments, community groups, healthcare practice groups, payer/insurer, etc.
   */
  Organization_resource_types: resource_types_Organization,
  /**
   * Defines an affiliation/assotiation/relationship between 2 distinct oganizations, that is not a part-of relationship/sub-division relationship.
   */
  OrganizationAffiliation_resource_types: resource_types_OrganizationAffiliation,
  /**
   * The parameters to the module. This collection specifies both the input and output parameters. Input parameters are provided by the caller as part of the $evaluate operation. Output parameters are included in the GuidanceResponse.
   */
  ParameterDefinition_data_types: data_types_ParameterDefinition,
  /**
   * This resource is a non-persisted resource used to pass information into and back from an [operation](operations.html). It has no other use, and there is no RESTful endpoint associated with it.
   */
  Parameters_resource_types: resource_types_Parameters,
  /**
   * Demographics and other administrative information about an individual or animal receiving care or other health-related services.
   */
  Patient_resource_types: resource_types_Patient,
  /**
   * This resource provides the status of the payment for goods and services rendered, and the request and response resource references.
   */
  PaymentNotice_resource_types: resource_types_PaymentNotice,
  /**
   * This resource provides the details including amount of a payment and allocates the payment items being paid.
   */
  PaymentReconciliation_resource_types: resource_types_PaymentReconciliation,
  /**
   * A time period defined by a start and end date and optionally time.
   */
  Period_data_types: data_types_Period,
  /**
   * Demographics and administrative information about a person independent of a specific health-related context.
   */
  Person_resource_types: resource_types_Person,
  /**
   * This resource allows for the definition of various types of plans as a sharable, consumable, and executable artifact. The resource is general enough to support the description of a broad range of clinical artifacts such as clinical decision support rules, order sets and protocols.
   */
  PlanDefinition_resource_types: resource_types_PlanDefinition,
  /**
   * A populatioof people with some set of grouping criteria.
   */
  Population_data_types: data_types_Population,
  /**
   * An integer with a value that is positive (e.g. >0)
   */
  PositiveInt_data_types: data_types_PositiveInt,
  /**
   * A person who is directly or indirectly involved in the provisioning of healthcare.
   */
  Practitioner_resource_types: resource_types_Practitioner,
  /**
   * A specific set of Roles/Locations/specialties/services that a practitioner may perform at an organization for a period of time.
   */
  PractitionerRole_resource_types: resource_types_PractitionerRole,
  /**
   * An action that is or was performed on or for a patient. This can be a physical intervention like an operation, or less invasive like long term services, counseling, or hypnotherapy.
   */
  Procedure_resource_types: resource_types_Procedure,
  /**
   * The marketing status describes the date when a medicinal product is actually put on the market or the date as of which it is no longer available.
   */
  ProdCharacteristic_data_types: data_types_ProdCharacteristic,
  /**
   * The shelf-life and storage information for a medicinal product item or container can be described using this class.
   */
  ProductShelfLife_data_types: data_types_ProductShelfLife,
  /**
   * Provenance of a resource is a record that describes entities and processes involved in producing and delivering or otherwise influencing that resource. Provenance provides a critical foundation for assessing authenticity, enabling trust, and allowing reproducibility. Provenance assertions are a form of contextual metadata and can themselves become important records with their own provenance. Provenance statement indicates clinical significance in terms of confidence in authenticity, reliability, and trustworthiness, integrity, and stage in lifecycle (e.g. Document Completion - has the artifact been legally authenticated), all of which may impact security, privacy, and trust policies.
   */
  Provenance_resource_types: resource_types_Provenance,
  /**
   * A measured amount (or an amount that can potentially be measured). Note that measured amounts include amounts that are not precisely quantified, including amounts involving arbitrary units and floating currencies.
   */
  Quantity_data_types: data_types_Quantity,
  /**
   * A structured set of questions intended to guide the collection of answers from end-users. Questionnaires provide detailed control over order, presentation, phraseology and grouping to allow coherent, consistent data collection.
   */
  Questionnaire_resource_types: resource_types_Questionnaire,
  /**
   * A structured set of questions and their answers. The questions are ordered and grouped into coherent subsets, corresponding to the structure of the grouping of the questionnaire being responded to.
   */
  QuestionnaireResponse_resource_types: resource_types_QuestionnaireResponse,
  /**
   * A set of ordered Quantities defined by a low and high limit.
   */
  Range_data_types: data_types_Range,
  /**
   * A relationship of two Quantity values - expressed as a numerator and a denominator.
   */
  Ratio_data_types: data_types_Ratio,
  /**
   * A reference from one resource to another.
   */
  Reference_data_types: data_types_Reference,
  /**
   * Related artifacts such as additional documentation, justification, or bibliographic references.
   */
  RelatedArtifact_data_types: data_types_RelatedArtifact,
  /**
   * Information about a person that is involved in the care for a patient, but who is not the target of healthcare, nor has a formal responsibility in the care process.
   */
  RelatedPerson_resource_types: resource_types_RelatedPerson,
  /**
   * A group of related requests that can be used to capture intended activities that have inter-dependencies such as "give this medication after that one".
   */
  RequestGroup_resource_types: resource_types_RequestGroup,
  /**
   * The ResearchDefinition resource describes the conditional state (population and any exposures being compared within the population) and outcome (if specified) that the knowledge (evidence, assertion, recommendation) is about.
   */
  ResearchDefinition_resource_types: resource_types_ResearchDefinition,
  /**
   * The ResearchElementDefinition resource describes a "PICO" element that knowledge (evidence, assertion, recommendation) is about.
   */
  ResearchElementDefinition_resource_types: resource_types_ResearchElementDefinition,
  /**
   * A process where a researcher or organization plans and then executes a series of steps intended to increase the field of healthcare-related knowledge.  This includes studies of safety, efficacy, comparative effectiveness and other information about medications, devices, therapies and other interventional and investigative techniques.  A ResearchStudy involves the gathering of information about human or animal subjects.
   */
  ResearchStudy_resource_types: resource_types_ResearchStudy,
  /**
   * A physical entity which is the primary unit of operational and/or administrative interest in a study.
   */
  ResearchSubject_resource_types: resource_types_ResearchSubject,
  /**
   * This is the base resource type for everything.
   */
  Resource_resource_types: resource_types_Resource,
  /**
   * An assessment of the likely outcome(s) for a patient or other subject as well as the likelihood of each outcome.
   */
  RiskAssessment_resource_types: resource_types_RiskAssessment,
  /**
   * The RiskEvidenceSynthesis resource describes the likelihood of an outcome in a population plus exposure state where the risk estimate is derived from a combination of research studies.
   */
  RiskEvidenceSynthesis_resource_types: resource_types_RiskEvidenceSynthesis,
  /**
   * A series of measurements taken by a device, with upper and lower limits. There may be more than one dimension in the data.
   */
  SampledData_data_types: data_types_SampledData,
  /**
   * A container for slots of time that may be available for booking appointments.
   */
  Schedule_resource_types: resource_types_Schedule,
  /**
   * A search parameter that defines a named search item that can be used to search/filter on a resource.
   */
  SearchParameter_resource_types: resource_types_SearchParameter,
  /**
   * A record of a request for service such as diagnostic investigations, treatments, or operations to be performed.
   */
  ServiceRequest_resource_types: resource_types_ServiceRequest,
  /**
   * A signature along with supporting context. The signature may be a digital signature that is cryptographic in nature, or some other signature acceptable to the domain. This other signature may be as simple as a graphical image representing a hand-written signature, or a signature ceremony Different signature approaches have different utilities.
   */
  Signature_data_types: data_types_Signature,
  SimpleQuantity_data_types: data_types_SimpleQuantity,
  /**
   * A slot of time on a schedule that may be available for booking appointments.
   */
  Slot_resource_types: resource_types_Slot,
  /**
   * A sample to be used for analysis.
   */
  Specimen_resource_types: resource_types_Specimen,
  /**
   * A kind of specimen with associated set of requirements.
   */
  SpecimenDefinition_resource_types: resource_types_SpecimenDefinition,
  /**
   * A sequence of Unicode characters
   */
  String_data_types: data_types_String,
  /**
   * A definition of a FHIR structure. This resource is used to describe the underlying resources, data types defined in FHIR, and also for describing extensions and constraints on resources and data types.
   */
  StructureDefinition_resource_types: resource_types_StructureDefinition,
  /**
   * A Map of relationships between 2 structures that can be used to transform data.
   */
  StructureMap_resource_types: resource_types_StructureMap,
  /**
   * The subscription resource is used to define a push-based subscription from a server to another system. Once a subscription is registered with the server, the server checks every resource that is created or updated, and if the resource matches the given criteria, it sends a message on the defined "channel" so that another system can take an appropriate action.
   */
  Subscription_resource_types: resource_types_Subscription,
  /**
   * A homogeneous material with a definite composition.
   */
  Substance_resource_types: resource_types_Substance,
  /**
   * Chemical substances are a single substance type whose primary defining element is the molecular structure. Chemical substances shall be defined on the basis of their complete covalent molecular structure; the presence of a salt (counter-ion) and/or solvates (water, alcohols) is also captured. Purity, grade, physical form or particle size are not taken into account in the definition of a chemical substance or in the assignment of a Substance ID.
   */
  SubstanceAmount_data_types: data_types_SubstanceAmount,
  /**
   * Nucleic acids are defined by three distinct elements: the base, sugar and linkage. Individual substance/moiety IDs will be created for each of these elements. The nucleotide sequence will be always entered in the 5’-3’ direction.
   */
  SubstanceNucleicAcid_resource_types: resource_types_SubstanceNucleicAcid,
  /**
   * Todo.
   */
  SubstancePolymer_resource_types: resource_types_SubstancePolymer,
  /**
   * A SubstanceProtein is defined as a single unit of a linear amino acid sequence, or a combination of subunits that are either covalently linked or have a defined invariant stoichiometric relationship. This includes all synthetic, recombinant and purified SubstanceProteins of defined sequence, whether the use is therapeutic or prophylactic. This set of elements will be used to describe albumins, coagulation factors, cytokines, growth factors, peptide/SubstanceProtein hormones, enzymes, toxins, toxoids, recombinant vaccines, and immunomodulators.
   */
  SubstanceProtein_resource_types: resource_types_SubstanceProtein,
  /**
   * Todo.
   */
  SubstanceReferenceInformation_resource_types: resource_types_SubstanceReferenceInformation,
  /**
   * Source material shall capture information on the taxonomic and anatomical origins as well as the fraction of a material that can result in or can be modified to form a substance. This set of data elements shall be used to define polymer substances isolated from biological matrices. Taxonomic and anatomical origins shall be described using a controlled vocabulary as required. This information is captured for naturally derived polymers ( . starch) and structurally diverse substances. For Organisms belonging to the Kingdom Plantae the Substance level defines the fresh material of a single species or infraspecies, the Herbal Drug and the Herbal preparation. For Herbal preparations, the fraction information will be captured at the Substance information level and additional information for herbal extracts will be captured at the Specified Substance Group 1 information level. See for further explanation the Substance Class: Structurally Diverse and the herbal annex.
   */
  SubstanceSourceMaterial_resource_types: resource_types_SubstanceSourceMaterial,
  /**
   * The detailed description of a substance, typically at a level beyond what is used for prescribing.
   */
  SubstanceSpecification_resource_types: resource_types_SubstanceSpecification,
  /**
   * Record of delivery of what is supplied.
   */
  SupplyDelivery_resource_types: resource_types_SupplyDelivery,
  /**
   * A record of a request for a medication, substance or device used in the healthcare setting.
   */
  SupplyRequest_resource_types: resource_types_SupplyRequest,
  /**
   * A task to be performed.
   */
  Task_resource_types: resource_types_Task,
  /**
   * A TerminologyCapabilities resource documents a set of capabilities (behaviors) of a FHIR Terminology Server that may be used as a statement of actual server functionality or a statement of required or desired server implementation.
   */
  TerminologyCapabilities_resource_types: resource_types_TerminologyCapabilities,
  /**
   * A summary of information based on the results of executing a TestScript.
   */
  TestReport_resource_types: resource_types_TestReport,
  /**
   * A structured set of tests against a FHIR server or client implementation to determine compliance against the FHIR specification.
   */
  TestScript_resource_types: resource_types_TestScript,
  /**
   * A time during the day, with no date specified
   */
  Time_data_types: data_types_Time,
  /**
   * Specifies an event that may occur multiple times. Timing schedules are used to record when things are planned, expected or requested to occur. The most common usage is in dosage instructions for medications. They are also used when planning care of various kinds, and may be used for reporting the schedule to which past regular activities were carried out.
   */
  Timing_data_types: data_types_Timing,
  /**
   * A description of a triggering event. Triggering events can be named events, data events, or periodic, as determined by the type element.
   */
  TriggerDefinition_data_types: data_types_TriggerDefinition,
  /**
   * An integer with a value that is not negative (e.g. >= 0)
   */
  UnsignedInt_data_types: data_types_UnsignedInt,
  /**
   * String of characters used to identify a name or a resource
   */
  Uri_data_types: data_types_Uri,
  /**
   * A URI that is a literal reference
   */
  Url_data_types: data_types_Url,
  /**
   * Specifies clinical/business/etc. metadata that can be used to retrieve, index and/or categorize an artifact. This metadata can either be specific to the applicable population (e.g., age category, DRG) or the specific context of care (e.g., venue, care setting, provider of care).
   */
  UsageContext_data_types: data_types_UsageContext,
  /**
   * A UUID, represented as a URI
   */
  Uuid_data_types: data_types_Uuid,
  /**
   * A ValueSet resource instance specifies a set of codes drawn from one or more code systems, intended for use in a particular context. Value sets link between [[[CodeSystem]]] definitions and their use in [coded elements](terminologies.html).
   */
  ValueSet_resource_types: resource_types_ValueSet,
  /**
   * Describes validation requirements, source(s), status and dates for one or more elements.
   */
  VerificationResult_resource_types: resource_types_VerificationResult,
  /**
   * An authorization for the provision of glasses and/or contact lenses to a patient.
   */
  VisionPrescription_resource_types: resource_types_VisionPrescription,
  /**
   * XHTML format, as defined by W3C, but restricted usage (mainly, no active content)
   */
  XHTML_data_types: data_types_XHTML,
};
const definition_resource_types_ActivityDefinition: Coding = {
  code: 'ActivityDefinition',
  display: 'ActivityDefinition',
  system: 'http://hl7.org/fhir/definition-resource-types',
};
const definition_resource_types_EventDefinition: Coding = {
  code: 'EventDefinition',
  display: 'EventDefinition',
  system: 'http://hl7.org/fhir/definition-resource-types',
};
const definition_resource_types_Measure: Coding = {
  code: 'Measure',
  display: 'Measure',
  system: 'http://hl7.org/fhir/definition-resource-types',
};
const definition_resource_types_OperationDefinition: Coding = {
  code: 'OperationDefinition',
  display: 'OperationDefinition',
  system: 'http://hl7.org/fhir/definition-resource-types',
};
const definition_resource_types_PlanDefinition: Coding = {
  code: 'PlanDefinition',
  display: 'PlanDefinition',
  system: 'http://hl7.org/fhir/definition-resource-types',
};
const definition_resource_types_Questionnaire: Coding = {
  code: 'Questionnaire',
  display: 'Questionnaire',
  system: 'http://hl7.org/fhir/definition-resource-types',
};
/**
 * A list of all the definition resource types defined in this version of the FHIR specification.
 */
export const DefinitionResourceTypes = {
  /**
   * This resource allows for the definition of some activity to be performed, independent of a particular patient, practitioner, or other performance context.
   */
  ActivityDefinition: definition_resource_types_ActivityDefinition,
  /**
   * The EventDefinition resource provides a reusable description of when a particular event can occur.
   */
  EventDefinition: definition_resource_types_EventDefinition,
  /**
   * The Measure resource provides the definition of a quality measure.
   */
  Measure: definition_resource_types_Measure,
  /**
   * A formal computable definition of an operation (on the RESTful interface) or a named query (using the search interaction).
   */
  OperationDefinition: definition_resource_types_OperationDefinition,
  /**
   * This resource allows for the definition of various types of plans as a sharable, consumable, and executable artifact. The resource is general enough to support the description of a broad range of clinical artifacts such as clinical decision support rules, order sets and protocols.
   */
  PlanDefinition: definition_resource_types_PlanDefinition,
  /**
   * A structured set of questions intended to guide the collection of answers from end-users. Questionnaires provide detailed control over order, presentation, phraseology and grouping to allow coherent, consistent data collection.
   */
  Questionnaire: definition_resource_types_Questionnaire,
};
const definition_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://terminology.hl7.org/CodeSystem/definition-status',
};
const definition_status_Draft: Coding = {
  code: 'draft',
  display: 'Draft',
  system: 'http://terminology.hl7.org/CodeSystem/definition-status',
};
const definition_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/definition-status',
};
const definition_status_Withdrawn: Coding = {
  code: 'withdrawn',
  display: 'Withdrawn',
  system: 'http://terminology.hl7.org/CodeSystem/definition-status',
};
/**
 * Codes identifying the lifecycle stage of a definition.
 */
export const DefinitionStatus = {
  /**
   * The definition is considered ready for use.
   */
  Active: definition_status_Active,
  /**
   * The definition is in the design stage and is not yet considered to be "ready for use".
   */
  Draft: definition_status_Draft,
  /**
   * The authoring/source system does not know which of the status values currently applies for this resource.  Note: This concept is not to be used for "other" - one of the listed statuses is presumed to apply,  but the authoring/source system does not know which.
   */
  Unknown: definition_status_Unknown,
  /**
   * The definition should no longer be used.
   */
  Withdrawn: definition_status_Withdrawn,
};
const definition_topic_Assessment: Coding = {
  code: 'assessment',
  display: 'Assessment',
  system: 'http://terminology.hl7.org/CodeSystem/definition-topic',
};
const definition_topic_Education: Coding = {
  code: 'education',
  display: 'Education',
  system: 'http://terminology.hl7.org/CodeSystem/definition-topic',
};
const definition_topic_Treatment: Coding = {
  code: 'treatment',
  display: 'Treatment',
  system: 'http://terminology.hl7.org/CodeSystem/definition-topic',
};
/**
 * High-level categorization of the definition, used for searching, sorting, and filtering.
 */
export const DefinitionTopic = {
  /**
   * The definition is related to assessment of the patient.
   */
  Assessment: definition_topic_Assessment,
  /**
   * The definition is related to education of the patient.
   */
  Education: definition_topic_Education,
  /**
   * The definition is related to treatment of the patient.
   */
  Treatment: definition_topic_Treatment,
};
const definition_use_DomainAnalysisModel: Coding = {
  code: 'archetype',
  display: 'Domain Analysis Model',
  system: 'http://terminology.hl7.org/CodeSystem/definition-use',
};
const definition_use_CustomResource: Coding = {
  code: 'custom-resource',
  display: 'Custom Resource',
  system: 'http://terminology.hl7.org/CodeSystem/definition-use',
};
const definition_use_DomainAnalysisModel_2: Coding = {
  code: 'dam',
  display: 'Domain Analysis Model',
  system: 'http://terminology.hl7.org/CodeSystem/definition-use',
};
const definition_use_FHIRStructure: Coding = {
  code: 'fhir-structure',
  display: 'FHIR Structure',
  system: 'http://terminology.hl7.org/CodeSystem/definition-use',
};
const definition_use_Template: Coding = {
  code: 'template',
  display: 'Template',
  system: 'http://terminology.hl7.org/CodeSystem/definition-use',
};
const definition_use_WireFormat: Coding = {
  code: 'wire-format',
  display: 'Wire Format',
  system: 'http://terminology.hl7.org/CodeSystem/definition-use',
};
/**
 * Structure Definition Use Codes / Keywords
 */
export const DefinitionUse = {
  /**
   * This structure captures an analysis of a domain
   */
  DomainAnalysisModel: definition_use_DomainAnalysisModel,
  /**
   * This structure is intended to be treated like a FHIR resource (e.g. on the FHIR API)
   */
  CustomResource: definition_use_CustomResource,
  /**
   * This structure captures an analysis of a domain
   */
  DomainAnalysisModel_2: definition_use_DomainAnalysisModel_2,
  /**
   * This structure is defined as part of the base FHIR Specification
   */
  FHIRStructure: definition_use_FHIRStructure,
  /**
   * This structure is a template (n.b: 'template' has many meanings)
   */
  Template: definition_use_Template,
  /**
   * This structure represents and existing structure (e.g. CDA, HL7 v2)
   */
  WireFormat: definition_use_WireFormat,
};
const DesignationUse_VAL900000000000003001: Coding = {
  code: '900000000000003001',
  system: 'http://snomed.info/sct',
};
const DesignationUse_VAL900000000000013009: Coding = {
  code: '900000000000013009',
  system: 'http://snomed.info/sct',
};
/**
 * Details of how a designation would be used
 */
export const DesignationUse = {
  VAL900000000000003001: DesignationUse_VAL900000000000003001,
  VAL900000000000013009: DesignationUse_VAL900000000000013009,
};
const v3_ActCode_ActSuppliedItemDetectedIssueCode: Coding = {
  code: '_ActSuppliedItemDetectedIssueCode',
  display: 'ActSuppliedItemDetectedIssueCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdministrationDetectedIssueCode: Coding = {
  code: '_AdministrationDetectedIssueCode',
  display: 'AdministrationDetectedIssueCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AppropriatenessDetectedIssueCode: Coding = {
  code: '_AppropriatenessDetectedIssueCode',
  display: 'AppropriatenessDetectedIssueCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InteractionDetectedIssueCode: Coding = {
  code: '_InteractionDetectedIssueCode',
  display: 'InteractionDetectedIssueCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SupplyDetectedIssueCode: Coding = {
  code: '_SupplyDetectedIssueCode',
  display: 'SupplyDetectedIssueCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TimingDetectedIssueCode: Coding = {
  code: '_TimingDetectedIssueCode',
  display: 'TimingDetectedIssueCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AlreadyPerformed: Coding = {
  code: 'ALLDONE',
  display: 'already performed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EndTooLateAlert: Coding = {
  code: 'ALRTENDLATE',
  display: 'end too late alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_StartTooLateAlert: Coding = {
  code: 'ALRTSTRTLATE',
  display: 'start too late alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DrugActionDetectedIssue: Coding = {
  code: 'DACT',
  display: 'drug action detected issue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DrugInteractionAlert: Coding = {
  code: 'DRG',
  display: 'Drug Interaction Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EndTooLateAlert_2: Coding = {
  code: 'ENDLATE',
  display: 'End Too Late Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FoodInteractionAlert: Coding = {
  code: 'FOOD',
  display: 'Food Interaction Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FulfillmentAlert: Coding = {
  code: 'FULFIL',
  display: 'fulfillment alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HeldSuspendedAlert: Coding = {
  code: 'HELD',
  display: 'held/suspended alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RecordRecordedAsHistorical: Coding = {
  code: 'HISTORIC',
  display: 'record recorded as historical',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OutsideRequestedTime: Coding = {
  code: 'INTERVAL',
  display: 'outside requested time',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TooSoonWithinFrequencyBasedOnTheUsage: Coding = {
  code: 'MINFREQ',
  display: 'too soon within frequency based on the usage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NaturalHealthProductAlert: Coding = {
  code: 'NHP',
  display: 'Natural Health Product Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NonPrescriptionInteractionAlert: Coding = {
  code: 'NONRX',
  display: 'Non-Prescription Interaction Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoLongerActionable: Coding = {
  code: 'NOTACTN',
  display: 'no longer actionable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NotEquivalentAlert: Coding = {
  code: 'NOTEQUIV',
  display: 'not equivalent alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NotGenericallyEquivalentAlert: Coding = {
  code: 'NOTEQUIVGEN',
  display: 'not generically equivalent alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NotTherapeuticallyEquivalentAlert: Coding = {
  code: 'NOTEQUIVTHER',
  display: 'not therapeutically equivalent alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ViolatesStatedPreferences: Coding = {
  code: 'PATPREF',
  display: 'violates stated preferences',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ViolatesStatedPreferencesAlternateAvailable: Coding = {
  code: 'PATPREFALT',
  display: 'violates stated preferences, alternate available',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PreviouslyIneffective: Coding = {
  code: 'PREVINEF',
  display: 'previously ineffective',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_StartTooLateAlert_2: Coding = {
  code: 'STRTLATE',
  display: 'Start Too Late Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TimingDetectedIssue: Coding = {
  code: 'TIME',
  display: 'timing detected issue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EventTimingIncorrectAlert: Coding = {
  code: 'TIMING',
  display: 'event timing incorrect alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RefillTooLateAlert: Coding = {
  code: 'TOOLATE',
  display: 'Refill Too Late Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RefillTooSoonAlert: Coding = {
  code: 'TOOSOON',
  display: 'Refill Too Soon Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TherapeuticProductAlert: Coding = {
  code: 'TPROD',
  display: 'Therapeutic Product Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
/**
 * Kinds of issues or contraindications, such as 'drug-drug interaction', 'duplicate therapy', etc.
 */
export const DetectedissueCategory = {
  /**
   * Identifies types of detected issues regarding the administration or supply of an item to a patient.
   */
  ActSuppliedItemDetectedIssueCode: v3_ActCode_ActSuppliedItemDetectedIssueCode,
  /**
   * Administration of the proposed therapy may be inappropriate or contraindicated as proposed
   */
  AdministrationDetectedIssueCode: v3_ActCode_AdministrationDetectedIssueCode,
  /**
   * AppropriatenessDetectedIssueCode
   */
  AppropriatenessDetectedIssueCode: v3_ActCode_AppropriatenessDetectedIssueCode,
  /**
   * InteractionDetectedIssueCode
   */
  InteractionDetectedIssueCode: v3_ActCode_InteractionDetectedIssueCode,
  /**
   * Supplying the product at this time may be inappropriate or indicate compliance issues with the associated therapy
   */
  SupplyDetectedIssueCode: v3_ActCode_SupplyDetectedIssueCode,
  /**
   * Proposed therapy may be inappropriate or ineffective based on the proposed start or end time.
   */
  TimingDetectedIssueCode: v3_ActCode_TimingDetectedIssueCode,
  /**
   * Definition:The requested action has already been performed and so this request has no effect
   */
  AlreadyPerformed: v3_ActCode_AlreadyPerformed,
  /**
   * Definition:Proposed therapy may be inappropriate or ineffective because the end of administration is too close to another planned therapy.
   */
  EndTooLateAlert: v3_ActCode_EndTooLateAlert,
  /**
   * Definition:Proposed therapy may be inappropriate or ineffective because the start of administration is too late after the onset of the condition.
   */
  StartTooLateAlert: v3_ActCode_StartTooLateAlert,
  /**
   * Description:Proposed therapy may be contraindicated or ineffective based on an existing or recent drug therapy.
   */
  DrugActionDetectedIssue: v3_ActCode_DrugActionDetectedIssue,
  /**
   * Proposed therapy may interact with an existing or recent drug therapy
   */
  DrugInteractionAlert: v3_ActCode_DrugInteractionAlert,
  /**
   * Proposed therapy may be inappropriate or ineffective because the end of administration is too close to another planned therapy
   */
  EndTooLateAlert_2: v3_ActCode_EndTooLateAlert_2,
  /**
   * Proposed therapy may interact with certain foods
   */
  FoodInteractionAlert: v3_ActCode_FoodInteractionAlert,
  /**
   * Definition:The therapy being performed is in some way out of alignment with the requested therapy.
   */
  FulfillmentAlert: v3_ActCode_FulfillmentAlert,
  /**
   * Definition:There should be no actions taken in fulfillment of a request that has been held or suspended.
   */
  HeldSuspendedAlert: v3_ActCode_HeldSuspendedAlert,
  /**
   * Description: While the record was accepted in the repository, there is a more recent version of a record of this type.
   */
  RecordRecordedAsHistorical: v3_ActCode_RecordRecordedAsHistorical,
  /**
   * Definition:The therapy action is being performed outside the bounds of the time period requested
   */
  OutsideRequestedTime: v3_ActCode_OutsideRequestedTime,
  /**
   * Definition:The therapy action is being performed too soon after the previous occurrence based on the requested frequency
   */
  TooSoonWithinFrequencyBasedOnTheUsage: v3_ActCode_TooSoonWithinFrequencyBasedOnTheUsage,
  /**
   * Proposed therapy may interact with existing or recent natural health product therapy
   */
  NaturalHealthProductAlert: v3_ActCode_NaturalHealthProductAlert,
  /**
   * Proposed therapy may interact with a non-prescription drug (e.g. alcohol, tobacco, Aspirin)
   */
  NonPrescriptionInteractionAlert: v3_ActCode_NonPrescriptionInteractionAlert,
  /**
   * Definition:The status of the request being fulfilled has changed such that it is no longer actionable.  This may be because the request has expired, has already been completely fulfilled or has been otherwise stopped or disabled.  (Not used for 'suspended' orders.)
   */
  NoLongerActionable: v3_ActCode_NoLongerActionable,
  /**
   * Definition:The therapy being performed is not sufficiently equivalent to the therapy which was requested.
   */
  NotEquivalentAlert: v3_ActCode_NotEquivalentAlert,
  /**
   * Definition:The therapy being performed is not generically equivalent (having the identical biological action) to the therapy which was requested.
   */
  NotGenericallyEquivalentAlert: v3_ActCode_NotGenericallyEquivalentAlert,
  /**
   * Definition:The therapy being performed is not therapeutically equivalent (having the same overall patient effect) to the therapy which was requested.
   */
  NotTherapeuticallyEquivalentAlert: v3_ActCode_NotTherapeuticallyEquivalentAlert,
  /**
   * Definition:The proposed therapy goes against preferences or consent constraints recorded in the patient's record.
   */
  ViolatesStatedPreferences: v3_ActCode_ViolatesStatedPreferences,
  /**
   * Definition:The proposed therapy goes against preferences or consent constraints recorded in the patient's record.  An alternate therapy meeting those constraints is available.
   */
  ViolatesStatedPreferencesAlternateAvailable: v3_ActCode_ViolatesStatedPreferencesAlternateAvailable,
  /**
   * Definition:The same or similar treatment has previously been attempted with the patient without achieving a positive effect.
   */
  PreviouslyIneffective: v3_ActCode_PreviouslyIneffective,
  /**
   * Proposed therapy may be inappropriate or ineffective because the start of administration is too late after the onset of the condition
   */
  StartTooLateAlert_2: v3_ActCode_StartTooLateAlert_2,
  /**
   * Description:Proposed therapy may be inappropriate or ineffective based on the proposed start or end time.
   */
  TimingDetectedIssue: v3_ActCode_TimingDetectedIssue,
  /**
   * Definition:The therapy is being performed at a time which diverges from the time the therapy was requested
   */
  EventTimingIncorrectAlert: v3_ActCode_EventTimingIncorrectAlert,
  /**
   * The patient is receiving a subsequent fill significantly later than would be expected based on the amount previously supplied and the therapy dosage instructions
   */
  RefillTooLateAlert: v3_ActCode_RefillTooLateAlert,
  /**
   * The patient is receiving a subsequent fill significantly earlier than would be expected based on the amount previously supplied and the therapy dosage instructions
   */
  RefillTooSoonAlert: v3_ActCode_RefillTooSoonAlert,
  /**
   * Proposed therapy may interact with an existing or recent therapeutic product
   */
  TherapeuticProductAlert: v3_ActCode_TherapeuticProductAlert,
};
const v3_ActCode_ActAdministrativeDetectedIssueManagementCode: Coding = {
  code: '_ActAdministrativeDetectedIssueManagementCode',
  display: 'ActAdministrativeDetectedIssueManagementCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActDetectedIssueManagementCode: Coding = {
  code: '_ActDetectedIssueManagementCode',
  display: 'ActDetectedIssueManagementCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AuthorizationIssueManagementCode: Coding = {
  code: '_AuthorizationIssueManagementCode',
  display: 'Authorization Issue Management Code',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TherapyAppropriate: Coding = {
  code: '1',
  display: 'Therapy Appropriate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ProvidedPatientEducation: Coding = {
  code: '10',
  display: 'Provided Patient Education',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AddedConcurrentTherapy: Coding = {
  code: '11',
  display: 'Added Concurrent Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TemporarilySuspendedConcurrentTherapy: Coding = {
  code: '12',
  display: 'Temporarily Suspended Concurrent Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_StoppedConcurrentTherapy: Coding = {
  code: '13',
  display: 'Stopped Concurrent Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SupplyAppropriate: Coding = {
  code: '14',
  display: 'Supply Appropriate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Replacement: Coding = {
  code: '15',
  display: 'Replacement',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_VacationSupply: Coding = {
  code: '16',
  display: 'Vacation Supply',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_WeekendSupply: Coding = {
  code: '17',
  display: 'Weekend Supply',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LeaveOfAbsence: Coding = {
  code: '18',
  display: 'Leave of Absence',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ConsultedSupplier: Coding = {
  code: '19',
  display: 'Consulted Supplier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AssessedPatient: Coding = {
  code: '2',
  display: 'Assessed Patient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdditionalQuantityOnSeparateDispense: Coding = {
  code: '20',
  display: 'additional quantity on separate dispense',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AuthorizationConfirmed: Coding = {
  code: '21',
  display: 'authorization confirmed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AppropriateIndicationOrDiagnosis: Coding = {
  code: '22',
  display: 'appropriate indication or diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PriorTherapyDocumented: Coding = {
  code: '23',
  display: 'prior therapy documented',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PatientExplanation: Coding = {
  code: '3',
  display: 'Patient Explanation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ConsultedOtherSource: Coding = {
  code: '4',
  display: 'Consulted Other Source',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ConsultedPrescriber: Coding = {
  code: '5',
  display: 'Consulted Prescriber',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PrescriberDeclinedChange: Coding = {
  code: '6',
  display: 'Prescriber Declined Change',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InteractingTherapyNoLongerActivePlanned: Coding = {
  code: '7',
  display: 'Interacting Therapy No Longer Active/Planned',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OtherActionTaken: Coding = {
  code: '8',
  display: 'Other Action Taken',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InstitutedOngoingMonitoringProgram: Coding = {
  code: '9',
  display: 'Instituted Ongoing Monitoring Program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EmergencyAuthorizationOverride: Coding = {
  code: 'EMAUTH',
  display: 'emergency authorization override',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
/**
 * Kinds of mitigating actions and observations that can be associated with a detected issue or contraindication, such as 'added concurrent therapy', 'prior therapy documented', etc.
 */
export const DetectedissueMitigationAction = {
  /**
   * Codes dealing with the management of Detected Issue observations for the administrative and patient administrative acts domains.
   */
  ActAdministrativeDetectedIssueManagementCode: v3_ActCode_ActAdministrativeDetectedIssueManagementCode,
  /**
   * Codes dealing with the management of Detected Issue observations
   */
  ActDetectedIssueManagementCode: v3_ActCode_ActDetectedIssueManagementCode,
  /**
   * Authorization Issue Management Code
   */
  AuthorizationIssueManagementCode: v3_ActCode_AuthorizationIssueManagementCode,
  /**
   * Confirmed drug therapy appropriate
   */
  TherapyAppropriate: v3_ActCode_TherapyAppropriate,
  /**
   * Provided education or training to the patient on appropriate therapy use
   */
  ProvidedPatientEducation: v3_ActCode_ProvidedPatientEducation,
  /**
   * Instituted an additional therapy to mitigate potential negative effects
   */
  AddedConcurrentTherapy: v3_ActCode_AddedConcurrentTherapy,
  /**
   * Suspended existing therapy that triggered interaction for the duration of this therapy
   */
  TemporarilySuspendedConcurrentTherapy: v3_ActCode_TemporarilySuspendedConcurrentTherapy,
  /**
   * Aborted existing therapy that triggered interaction.
   */
  StoppedConcurrentTherapy: v3_ActCode_StoppedConcurrentTherapy,
  /**
   * Confirmed supply action appropriate
   */
  SupplyAppropriate: v3_ActCode_SupplyAppropriate,
  /**
   * Patient's existing supply was lost/wasted
   */
  Replacement: v3_ActCode_Replacement,
  /**
   * Supply date is due to patient vacation
   */
  VacationSupply: v3_ActCode_VacationSupply,
  /**
   * Supply date is intended to carry patient over weekend
   */
  WeekendSupply: v3_ActCode_WeekendSupply,
  /**
   * Supply is intended for use during a leave of absence from an institution.
   */
  LeaveOfAbsence: v3_ActCode_LeaveOfAbsence,
  /**
   * Consulted other supplier/pharmacy, therapy confirmed
   */
  ConsultedSupplier: v3_ActCode_ConsultedSupplier,
  /**
   * Assessed patient, therapy is appropriate
   */
  AssessedPatient: v3_ActCode_AssessedPatient,
  /**
   * Description: Supply is different than expected as an additional quantity has been supplied in a separate dispense.
   */
  AdditionalQuantityOnSeparateDispense: v3_ActCode_AdditionalQuantityOnSeparateDispense,
  /**
   * Description: Indicates that the permissions have been externally verified and the request should be processed.
   */
  AuthorizationConfirmed: v3_ActCode_AuthorizationConfirmed,
  /**
   * Description: The patient has the appropriate indication or diagnosis for the action to be taken.
   */
  AppropriateIndicationOrDiagnosis: v3_ActCode_AppropriateIndicationOrDiagnosis,
  /**
   * Description: It has been confirmed that the appropriate pre-requisite therapy has been tried.
   */
  PriorTherapyDocumented: v3_ActCode_PriorTherapyDocumented,
  /**
   * Patient gave adequate explanation
   */
  PatientExplanation: v3_ActCode_PatientExplanation,
  /**
   * Consulted other supply source, therapy still appropriate
   */
  ConsultedOtherSource: v3_ActCode_ConsultedOtherSource,
  /**
   * Consulted prescriber, therapy confirmed
   */
  ConsultedPrescriber: v3_ActCode_ConsultedPrescriber,
  /**
   * Consulted prescriber and recommended change, prescriber declined
   */
  PrescriberDeclinedChange: v3_ActCode_PrescriberDeclinedChange,
  /**
   * Concurrent therapy triggering alert is no longer on-going or planned
   */
  InteractingTherapyNoLongerActivePlanned: v3_ActCode_InteractingTherapyNoLongerActivePlanned,
  /**
   * Order is performed as issued, but other action taken to mitigate potential adverse effects
   */
  OtherActionTaken: v3_ActCode_OtherActionTaken,
  /**
   * Arranged to monitor patient for adverse effects
   */
  InstitutedOngoingMonitoringProgram: v3_ActCode_InstitutedOngoingMonitoringProgram,
  /**
   * Used to temporarily override normal authorization rules to gain access to data in a case of emergency. Use of this override code will typically be monitored, and a procedure to verify its proper use may be triggered when used.
   */
  EmergencyAuthorizationOverride: v3_ActCode_EmergencyAuthorizationOverride,
};
const detectedissue_severity_High: Coding = {
  code: 'high',
  display: 'High',
  system: 'http://hl7.org/fhir/detectedissue-severity',
};
const detectedissue_severity_Low: Coding = {
  code: 'low',
  display: 'Low',
  system: 'http://hl7.org/fhir/detectedissue-severity',
};
const detectedissue_severity_Moderate: Coding = {
  code: 'moderate',
  display: 'Moderate',
  system: 'http://hl7.org/fhir/detectedissue-severity',
};
/**
 * Indicates the potential degree of impact of the identified issue on the patient.
 */
export const DetectedissueSeverity = {
  /**
   * Indicates the issue may be life-threatening or has the potential to cause permanent injury.
   */
  High: detectedissue_severity_High,
  /**
   * Indicates the issue may result in some adverse consequences but is unlikely to substantially affect the situation of the subject.
   */
  Low: detectedissue_severity_Low,
  /**
   * Indicates the issue may result in noticeable adverse consequences but is unlikely to be life-threatening or cause permanent injury.
   */
  Moderate: detectedissue_severity_Moderate,
};
const DeviceComponentProperty_MDCREGCERTDATACONTINUACERTDEVLIST: Coding = {
  code: '532353',
  display: 'MDC_REG_CERT_DATA_CONTINUA_CERT_DEV_LIST',
  system: 'urn:iso:std:iso:11073:10101',
};
const DeviceComponentProperty_MDCREGCERTDATACONTINUAREGSTATUS: Coding = {
  code: '532354',
  display: 'MDC_REG_CERT_DATA_CONTINUA_REG_STATUS',
  system: 'urn:iso:std:iso:11073:10101',
};
const DeviceComponentProperty_MDCREGCERTDATACONTINUAPHGCERTLIST: Coding = {
  code: '532355',
  display: 'MDC_REG_CERT_DATA_CONTINUA_PHG_CERT_LIST',
  system: 'urn:iso:std:iso:11073:10101',
};
const DeviceComponentProperty_MDCTIMECAPSTATE: Coding = {
  code: '68219',
  display: 'MDC_TIME_CAP_STATE',
  system: 'urn:iso:std:iso:11073:10101',
};
const DeviceComponentProperty_MDCTIMESYNCPROTOCOL: Coding = {
  code: '68220',
  display: 'MDC_TIME_SYNC_PROTOCOL',
  system: 'urn:iso:std:iso:11073:10101',
};
const DeviceComponentProperty_MDCTIMESYNCACCURACY: Coding = {
  code: '68221',
  display: 'MDC_TIME_SYNC_ACCURACY',
  system: 'urn:iso:std:iso:11073:10101',
};
const DeviceComponentProperty_MDCTIMERESABS: Coding = {
  code: '68222',
  display: 'MDC_TIME_RES_ABS',
  system: 'urn:iso:std:iso:11073:10101',
};
const DeviceComponentProperty_MDCTIMERESREL: Coding = {
  code: '68223',
  display: 'MDC_TIME_RES_REL',
  system: 'urn:iso:std:iso:11073:10101',
};
const DeviceComponentProperty_MDCTIMERESRELHIRES: Coding = {
  code: '68224',
  display: 'MDC_TIME_RES_REL_HI_RES',
  system: 'urn:iso:std:iso:11073:10101',
};
const DeviceComponentProperty_MDCTIMERESBO: Coding = {
  code: '68226',
  display: 'MDC_TIME_RES_BO',
  system: 'urn:iso:std:iso:11073:10101',
};
/**
 * Codes for identifying device properties.   This is based upon IEEE/HCD specified codes.
 */
export const DeviceComponentProperty = {
  MDCREGCERTDATACONTINUACERTDEVLIST: DeviceComponentProperty_MDCREGCERTDATACONTINUACERTDEVLIST,
  MDCREGCERTDATACONTINUAREGSTATUS: DeviceComponentProperty_MDCREGCERTDATACONTINUAREGSTATUS,
  MDCREGCERTDATACONTINUAPHGCERTLIST: DeviceComponentProperty_MDCREGCERTDATACONTINUAPHGCERTLIST,
  MDCTIMECAPSTATE: DeviceComponentProperty_MDCTIMECAPSTATE,
  MDCTIMESYNCPROTOCOL: DeviceComponentProperty_MDCTIMESYNCPROTOCOL,
  MDCTIMESYNCACCURACY: DeviceComponentProperty_MDCTIMESYNCACCURACY,
  MDCTIMERESABS: DeviceComponentProperty_MDCTIMERESABS,
  MDCTIMERESREL: DeviceComponentProperty_MDCTIMERESREL,
  MDCTIMERESRELHIRES: DeviceComponentProperty_MDCTIMERESRELHIRES,
  MDCTIMERESBO: DeviceComponentProperty_MDCTIMERESBO,
};
const device_definition_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/device-definition-status',
};
const device_definition_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/device-definition-status',
};
const device_definition_status_Inactive: Coding = {
  code: 'inactive',
  display: 'Inactive',
  system: 'http://hl7.org/fhir/device-definition-status',
};
const device_definition_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/device-definition-status',
};
/**
 * Codes representing the current status of the device - on, off, suspended, etc.
 */
export const DeviceDefinitionStatus = {
  /**
   * The device is available for use.  Note: For *implanted devices*  this means that the device is implanted in the patient.
   */
  Active: device_definition_status_Active,
  /**
   * The device was entered in error and voided.
   */
  EnteredInError: device_definition_status_EnteredInError,
  /**
   * The device is no longer available for use (e.g. lost, expired, damaged).  Note: For *implanted devices*  this means that the device has been removed from the patient.
   */
  Inactive: device_definition_status_Inactive,
  /**
   * The status of the device has not been determined.
   */
  Unknown: device_definition_status_Unknown,
};
const device_nametype_ManufacturerName: Coding = {
  code: 'manufacturer-name',
  display: 'Manufacturer name',
  system: 'http://hl7.org/fhir/device-nametype',
};
const device_nametype_ModelName: Coding = {
  code: 'model-name',
  display: 'Model name',
  system: 'http://hl7.org/fhir/device-nametype',
};
const device_nametype_Other: Coding = {
  code: 'other',
  display: 'other',
  system: 'http://hl7.org/fhir/device-nametype',
};
const device_nametype_PatientReportedName: Coding = {
  code: 'patient-reported-name',
  display: 'Patient Reported name',
  system: 'http://hl7.org/fhir/device-nametype',
};
const device_nametype_UDILabelName: Coding = {
  code: 'udi-label-name',
  display: 'UDI Label name',
  system: 'http://hl7.org/fhir/device-nametype',
};
const device_nametype_UserFriendlyName: Coding = {
  code: 'user-friendly-name',
  display: 'User Friendly name',
  system: 'http://hl7.org/fhir/device-nametype',
};
/**
 * The type of name the device is referred by.
 */
export const DeviceNametype = {
  /**
   * Manufacturer name.
   */
  ManufacturerName: device_nametype_ManufacturerName,
  /**
   * Model name.
   */
  ModelName: device_nametype_ModelName,
  /**
   * other.
   */
  Other: device_nametype_Other,
  /**
   * Patient Reported name.
   */
  PatientReportedName: device_nametype_PatientReportedName,
  /**
   * UDI Label name.
   */
  UDILabelName: device_nametype_UDILabelName,
  /**
   * User Friendly name.
   */
  UserFriendlyName: device_nametype_UserFriendlyName,
};
const DeviceSafety_LabeledAsContainingNaturalRubberLatex: Coding = {
  code: 'C101673',
  display: 'Labeled as Containing Natural Rubber Latex',
  system: 'urn:oid:2.16.840.1.113883.3.26.1.1',
};
const DeviceSafety_NotMadeWithNaturalRubberLatex: Coding = {
  code: 'C106038',
  display: 'Not Made with Natural Rubber Latex',
  system: 'urn:oid:2.16.840.1.113883.3.26.1.1',
};
const DeviceSafety_MagneticResonanceSafe: Coding = {
  code: 'C106045',
  display: 'Magnetic Resonance Safe',
  system: 'urn:oid:2.16.840.1.113883.3.26.1.1',
};
const DeviceSafety_MagneticResonanceConditional: Coding = {
  code: 'C106046',
  display: 'Magnetic Resonance Conditional',
  system: 'urn:oid:2.16.840.1.113883.3.26.1.1',
};
const DeviceSafety_MagneticResonanceUnsafe: Coding = {
  code: 'C106047',
  display: 'Magnetic Resonance Unsafe',
  system: 'urn:oid:2.16.840.1.113883.3.26.1.1',
};
const DeviceSafety_LabelingDoesNotContainMRISafetyInformation: Coding = {
  code: 'C113844',
  display: 'Labeling does not Contain MRI Safety Information',
  system: 'urn:oid:2.16.840.1.113883.3.26.1.1',
};
/**
 * Codes used to identify medical devices safety characteristics. These codes are taken from the [NCI Thesaurus](https://ncit.nci.nih.gov/ncitbrowser/pages/home.jsf) and are provided here as a suggestive example.
 */
export const DeviceSafety = {
  LabeledAsContainingNaturalRubberLatex: DeviceSafety_LabeledAsContainingNaturalRubberLatex,
  NotMadeWithNaturalRubberLatex: DeviceSafety_NotMadeWithNaturalRubberLatex,
  MagneticResonanceSafe: DeviceSafety_MagneticResonanceSafe,
  MagneticResonanceConditional: DeviceSafety_MagneticResonanceConditional,
  MagneticResonanceUnsafe: DeviceSafety_MagneticResonanceUnsafe,
  LabelingDoesNotContainMRISafetyInformation: DeviceSafety_LabelingDoesNotContainMRISafetyInformation,
};
const device_statement_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/device-statement-status',
};
const device_statement_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/device-statement-status',
};
const device_statement_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/device-statement-status',
};
const device_statement_status_Intended: Coding = {
  code: 'intended',
  display: 'Intended',
  system: 'http://hl7.org/fhir/device-statement-status',
};
const device_statement_status_OnHold: Coding = {
  code: 'on-hold',
  display: 'On Hold',
  system: 'http://hl7.org/fhir/device-statement-status',
};
const device_statement_status_Stopped: Coding = {
  code: 'stopped',
  display: 'Stopped',
  system: 'http://hl7.org/fhir/device-statement-status',
};
/**
 * A coded concept indicating the current status of the Device Usage.
 */
export const DeviceStatementStatus = {
  /**
   * The device is still being used.
   */
  Active: device_statement_status_Active,
  /**
   * The device is no longer being used.
   */
  Completed: device_statement_status_Completed,
  /**
   * The statement was recorded incorrectly.
   */
  EnteredInError: device_statement_status_EnteredInError,
  /**
   * The device may be used at some time in the future.
   */
  Intended: device_statement_status_Intended,
  /**
   * Actions implied by the statement have been temporarily halted, but are expected to continue later. May also be called "suspended".
   */
  OnHold: device_statement_status_OnHold,
  /**
   * Actions implied by the statement have been permanently halted, before all of them occurred.
   */
  Stopped: device_statement_status_Stopped,
};
const device_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/device-status',
};
const device_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/device-status',
};
const device_status_Inactive: Coding = {
  code: 'inactive',
  display: 'Inactive',
  system: 'http://hl7.org/fhir/device-status',
};
const device_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/device-status',
};
/**
 * The availability status of the device.
 */
export const DeviceStatus = {
  /**
   * The device is available for use.  Note: For *implanted devices*  this means that the device is implanted in the patient.
   */
  Active: device_status_Active,
  /**
   * The device was entered in error and voided.
   */
  EnteredInError: device_status_EnteredInError,
  /**
   * The device is no longer available for use (e.g. lost, expired, damaged).  Note: For *implanted devices*  this means that the device has been removed from the patient.
   */
  Inactive: device_status_Inactive,
  /**
   * The status of the device has not been determined.
   */
  Unknown: device_status_Unknown,
};
const device_status_reason_HardwareDisconnected: Coding = {
  code: 'hw-discon',
  display: 'Hardware Disconnected',
  system: 'http://terminology.hl7.org/CodeSystem/device-status-reason',
};
const device_status_reason_NotReady: Coding = {
  code: 'not-ready',
  display: 'Not Ready',
  system: 'http://terminology.hl7.org/CodeSystem/device-status-reason',
};
const device_status_reason_Off: Coding = {
  code: 'off',
  display: 'Off',
  system: 'http://terminology.hl7.org/CodeSystem/device-status-reason',
};
const device_status_reason_Offline: Coding = {
  code: 'offline',
  display: 'Offline',
  system: 'http://terminology.hl7.org/CodeSystem/device-status-reason',
};
const device_status_reason_Online: Coding = {
  code: 'online',
  display: 'Online',
  system: 'http://terminology.hl7.org/CodeSystem/device-status-reason',
};
const device_status_reason_Paused: Coding = {
  code: 'paused',
  display: 'Paused',
  system: 'http://terminology.hl7.org/CodeSystem/device-status-reason',
};
const device_status_reason_Standby: Coding = {
  code: 'standby',
  display: 'Standby',
  system: 'http://terminology.hl7.org/CodeSystem/device-status-reason',
};
const device_status_reason_TransducerDisconnected: Coding = {
  code: 'transduc-discon',
  display: 'Transducer Disconnected',
  system: 'http://terminology.hl7.org/CodeSystem/device-status-reason',
};
/**
 * The availability status reason of the device.
 */
export const DeviceStatusReason = {
  /**
   * The device hardware is disconnected.
   */
  HardwareDisconnected: device_status_reason_HardwareDisconnected,
  /**
   * The device is not ready.
   */
  NotReady: device_status_reason_NotReady,
  /**
   * The device is off.
   */
  Off: device_status_reason_Off,
  /**
   * The device is offline.
   */
  Offline: device_status_reason_Offline,
  /**
   * The device is off.
   */
  Online: device_status_reason_Online,
  /**
   * The device is paused.
   */
  Paused: device_status_reason_Paused,
  /**
   * The device is ready but not actively operating.
   */
  Standby: device_status_reason_Standby,
  /**
   * The device transducer is disconnected.
   */
  TransducerDisconnected: device_status_reason_TransducerDisconnected,
};
const diagnosis_role_AdmissionDiagnosis: Coding = {
  code: 'AD',
  display: 'Admission diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/diagnosis-role',
};
const diagnosis_role_Billing: Coding = {
  code: 'billing',
  display: 'Billing',
  system: 'http://terminology.hl7.org/CodeSystem/diagnosis-role',
};
const diagnosis_role_ChiefComplaint: Coding = {
  code: 'CC',
  display: 'Chief complaint',
  system: 'http://terminology.hl7.org/CodeSystem/diagnosis-role',
};
const diagnosis_role_ComorbidityDiagnosis: Coding = {
  code: 'CM',
  display: 'Comorbidity diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/diagnosis-role',
};
const diagnosis_role_DischargeDiagnosis: Coding = {
  code: 'DD',
  display: 'Discharge diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/diagnosis-role',
};
const diagnosis_role_PostOpDiagnosis: Coding = {
  code: 'post-op',
  display: 'post-op diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/diagnosis-role',
};
const diagnosis_role_PreOpDiagnosis: Coding = {
  code: 'pre-op',
  display: 'pre-op diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/diagnosis-role',
};
/**
 * This value set defines a set of codes that can be used to express the role of a diagnosis on the Encounter or EpisodeOfCare record.
 */
export const DiagnosisRole = {
  AdmissionDiagnosis: diagnosis_role_AdmissionDiagnosis,
  Billing: diagnosis_role_Billing,
  ChiefComplaint: diagnosis_role_ChiefComplaint,
  ComorbidityDiagnosis: diagnosis_role_ComorbidityDiagnosis,
  DischargeDiagnosis: diagnosis_role_DischargeDiagnosis,
  PostOpDiagnosis: diagnosis_role_PostOpDiagnosis,
  PreOpDiagnosis: diagnosis_role_PreOpDiagnosis,
};
const diagnostic_report_status_Amended: Coding = {
  code: 'amended',
  display: 'Amended',
  system: 'http://hl7.org/fhir/diagnostic-report-status',
};
const diagnostic_report_status_Appended: Coding = {
  code: 'appended',
  display: 'Appended',
  system: 'http://hl7.org/fhir/diagnostic-report-status',
};
const diagnostic_report_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/diagnostic-report-status',
};
const diagnostic_report_status_Corrected: Coding = {
  code: 'corrected',
  display: 'Corrected',
  system: 'http://hl7.org/fhir/diagnostic-report-status',
};
const diagnostic_report_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/diagnostic-report-status',
};
const diagnostic_report_status_Final: Coding = {
  code: 'final',
  display: 'Final',
  system: 'http://hl7.org/fhir/diagnostic-report-status',
};
const diagnostic_report_status_Partial: Coding = {
  code: 'partial',
  display: 'Partial',
  system: 'http://hl7.org/fhir/diagnostic-report-status',
};
const diagnostic_report_status_Preliminary: Coding = {
  code: 'preliminary',
  display: 'Preliminary',
  system: 'http://hl7.org/fhir/diagnostic-report-status',
};
const diagnostic_report_status_Registered: Coding = {
  code: 'registered',
  display: 'Registered',
  system: 'http://hl7.org/fhir/diagnostic-report-status',
};
const diagnostic_report_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/diagnostic-report-status',
};
/**
 * The status of the diagnostic report.
 */
export const DiagnosticReportStatus = {
  /**
   * Subsequent to being final, the report has been modified.  This includes any change in the results, diagnosis, narrative text, or other content of a report that has been issued.
   */
  Amended: diagnostic_report_status_Amended,
  /**
   * Subsequent to being final, the report has been modified by adding new content. The existing content is unchanged.
   */
  Appended: diagnostic_report_status_Appended,
  /**
   * The report is unavailable because the measurement was not started or not completed (also sometimes called "aborted").
   */
  Cancelled: diagnostic_report_status_Cancelled,
  /**
   * Subsequent to being final, the report has been modified  to correct an error in the report or referenced results.
   */
  Corrected: diagnostic_report_status_Corrected,
  /**
   * The report has been withdrawn following a previous final release.  This electronic record should never have existed, though it is possible that real-world decisions were based on it. (If real-world activity has occurred, the status should be "cancelled" rather than "entered-in-error".).
   */
  EnteredInError: diagnostic_report_status_EnteredInError,
  /**
   * The report is complete and verified by an authorized person.
   */
  Final: diagnostic_report_status_Final,
  /**
   * This is a partial (e.g. initial, interim or preliminary) report: data in the report may be incomplete or unverified.
   */
  Partial: diagnostic_report_status_Partial,
  /**
   * Verified early results are available, but not all  results are final.
   */
  Preliminary: diagnostic_report_status_Preliminary,
  /**
   * The existence of the report is registered, but there is nothing yet available.
   */
  Registered: diagnostic_report_status_Registered,
  /**
   * The authoring/source system does not know which of the status values currently applies for this observation. Note: This concept is not to be used for "other" - one of the listed statuses is presumed to apply, but the authoring/source system does not know which.
   */
  Unknown: diagnostic_report_status_Unknown,
};
const v2_0074_Audiology: Coding = {
  code: 'AU',
  display: 'Audiology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_BloodGases: Coding = {
  code: 'BG',
  display: 'Blood Gases',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_BloodBank: Coding = {
  code: 'BLB',
  display: 'Blood Bank',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Cytogenetics: Coding = {
  code: 'CG',
  display: 'Cytogenetics',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Chemistry: Coding = {
  code: 'CH',
  display: 'Chemistry',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Cytopathology: Coding = {
  code: 'CP',
  display: 'Cytopathology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_CATScan: Coding = {
  code: 'CT',
  display: 'CAT Scan',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_CardiacCatheterization: Coding = {
  code: 'CTH',
  display: 'Cardiac Catheterization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_CardiacUltrasound: Coding = {
  code: 'CUS',
  display: 'Cardiac Ultrasound',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_ElectrocardiacEGEKGEECHolter: Coding = {
  code: 'EC',
  display: 'Electrocardiac (e.g., EKG,  EEC, Holter)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_ElectroneuroEEGEMGEPPSG: Coding = {
  code: 'EN',
  display: 'Electroneuro (EEG, EMG,EP,PSG)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Genetics: Coding = {
  code: 'GE',
  display: 'Genetics',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Hematology: Coding = {
  code: 'HM',
  display: 'Hematology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_BedsideICUMonitoring: Coding = {
  code: 'ICU',
  display: 'Bedside ICU Monitoring',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Immunology: Coding = {
  code: 'IMM',
  display: 'Immunology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Laboratory: Coding = {
  code: 'LAB',
  display: 'Laboratory',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Microbiology: Coding = {
  code: 'MB',
  display: 'Microbiology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Mycobacteriology: Coding = {
  code: 'MCB',
  display: 'Mycobacteriology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Mycology: Coding = {
  code: 'MYC',
  display: 'Mycology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_NuclearMagneticResonance: Coding = {
  code: 'NMR',
  display: 'Nuclear Magnetic Resonance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_NuclearMedicineScan: Coding = {
  code: 'NMS',
  display: 'Nuclear Medicine Scan',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_NursingServiceMeasures: Coding = {
  code: 'NRS',
  display: 'Nursing Service Measures',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_OutsideLab: Coding = {
  code: 'OSL',
  display: 'Outside Lab',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_OccupationalTherapy: Coding = {
  code: 'OT',
  display: 'Occupational Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Other: Coding = {
  code: 'OTH',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_OBUltrasound: Coding = {
  code: 'OUS',
  display: 'OB Ultrasound',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_PulmonaryFunction: Coding = {
  code: 'PF',
  display: 'Pulmonary Function',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Pharmacy: Coding = {
  code: 'PHR',
  display: 'Pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_PhysicianHxDxAdmissionNoteEtc: Coding = {
  code: 'PHY',
  display: 'Physician (Hx. Dx, admission note, etc.)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_PhysicalTherapy: Coding = {
  code: 'PT',
  display: 'Physical Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Radiology: Coding = {
  code: 'RAD',
  display: 'Radiology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_RespiratoryCareTherapy: Coding = {
  code: 'RC',
  display: 'Respiratory Care (therapy)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_RadiationTherapy: Coding = {
  code: 'RT',
  display: 'Radiation Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_RadiologyUltrasound: Coding = {
  code: 'RUS',
  display: 'Radiology Ultrasound',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Radiograph: Coding = {
  code: 'RX',
  display: 'Radiograph',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_SurgicalPathology: Coding = {
  code: 'SP',
  display: 'Surgical Pathology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Serology: Coding = {
  code: 'SR',
  display: 'Serology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Toxicology: Coding = {
  code: 'TX',
  display: 'Toxicology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Virology: Coding = {
  code: 'VR',
  display: 'Virology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_VascularUltrasound: Coding = {
  code: 'VUS',
  display: 'Vascular Ultrasound',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
const v2_0074_Cineradiograph: Coding = {
  code: 'XRC',
  display: 'Cineradiograph',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
};
/**
 * This value set includes all the codes in HL7 V2 table 0074.
 */
export const DiagnosticServiceSections = {
  Audiology: v2_0074_Audiology,
  BloodGases: v2_0074_BloodGases,
  BloodBank: v2_0074_BloodBank,
  Cytogenetics: v2_0074_Cytogenetics,
  Chemistry: v2_0074_Chemistry,
  Cytopathology: v2_0074_Cytopathology,
  CATScan: v2_0074_CATScan,
  CardiacCatheterization: v2_0074_CardiacCatheterization,
  CardiacUltrasound: v2_0074_CardiacUltrasound,
  ElectrocardiacEGEKGEECHolter: v2_0074_ElectrocardiacEGEKGEECHolter,
  ElectroneuroEEGEMGEPPSG: v2_0074_ElectroneuroEEGEMGEPPSG,
  Genetics: v2_0074_Genetics,
  Hematology: v2_0074_Hematology,
  BedsideICUMonitoring: v2_0074_BedsideICUMonitoring,
  Immunology: v2_0074_Immunology,
  Laboratory: v2_0074_Laboratory,
  Microbiology: v2_0074_Microbiology,
  Mycobacteriology: v2_0074_Mycobacteriology,
  Mycology: v2_0074_Mycology,
  NuclearMagneticResonance: v2_0074_NuclearMagneticResonance,
  NuclearMedicineScan: v2_0074_NuclearMedicineScan,
  NursingServiceMeasures: v2_0074_NursingServiceMeasures,
  OutsideLab: v2_0074_OutsideLab,
  OccupationalTherapy: v2_0074_OccupationalTherapy,
  Other: v2_0074_Other,
  OBUltrasound: v2_0074_OBUltrasound,
  PulmonaryFunction: v2_0074_PulmonaryFunction,
  Pharmacy: v2_0074_Pharmacy,
  PhysicianHxDxAdmissionNoteEtc: v2_0074_PhysicianHxDxAdmissionNoteEtc,
  PhysicalTherapy: v2_0074_PhysicalTherapy,
  Radiology: v2_0074_Radiology,
  RespiratoryCareTherapy: v2_0074_RespiratoryCareTherapy,
  RadiationTherapy: v2_0074_RadiationTherapy,
  RadiologyUltrasound: v2_0074_RadiologyUltrasound,
  Radiograph: v2_0074_Radiograph,
  SurgicalPathology: v2_0074_SurgicalPathology,
  Serology: v2_0074_Serology,
  Toxicology: v2_0074_Toxicology,
  Virology: v2_0074_Virology,
  VascularUltrasound: v2_0074_VascularUltrasound,
  Cineradiograph: v2_0074_Cineradiograph,
};
const dicom_dcim_Film: Coding = {
  code: '110010',
  display: 'Film',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_USBDiskEmulation: Coding = {
  code: '110030',
  display: 'USB Disk Emulation',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_Email: Coding = {
  code: '110031',
  display: 'Email',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_CD: Coding = {
  code: '110032',
  display: 'CD',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_DVD: Coding = {
  code: '110033',
  display: 'DVD',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_CompactFlash: Coding = {
  code: '110034',
  display: 'Compact Flash',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_MultiMediaCard: Coding = {
  code: '110035',
  display: 'Multi-media Card',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_SecureDigitalCard: Coding = {
  code: '110036',
  display: 'Secure Digital Card',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_URI: Coding = {
  code: '110037',
  display: 'URI',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_PaperDocument: Coding = {
  code: '110038',
  display: 'Paper Document',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
/**
 * Media Type Code
 */
export const Dicm405Mediatype = {
  /**
   * Film type of output
   */
  Film: dicom_dcim_Film,
  /**
   * A device that connects using the USB hard drive interface. These may be USB-Sticks, portable hard drives, and other technologies
   */
  USBDiskEmulation: dicom_dcim_USBDiskEmulation,
  /**
   * Email and email attachments used as a media for data transport
   */
  Email: dicom_dcim_Email,
  /**
   * CD-R, CD-ROM, and CD-RW media used for data transport
   */
  CD: dicom_dcim_CD,
  /**
   * DVD, DVD-RAM, and other DVD formatted media used for data transport
   */
  DVD: dicom_dcim_DVD,
  /**
   * Media that comply with the Compact Flash standard
   */
  CompactFlash: dicom_dcim_CompactFlash,
  /**
   * Media that comply with the Multi-media Card standard
   */
  MultiMediaCard: dicom_dcim_MultiMediaCard,
  /**
   * Media that comply with the Secure Digital Card standard
   */
  SecureDigitalCard: dicom_dcim_SecureDigitalCard,
  /**
   * URI Identifier for network or other resource, see RFC 3968
   */
  URI: dicom_dcim_URI,
  /**
   * Any paper or similar document
   */
  PaperDocument: dicom_dcim_PaperDocument,
};
const discriminator_type_Exists: Coding = {
  code: 'exists',
  display: 'Exists',
  system: 'http://hl7.org/fhir/discriminator-type',
};
const discriminator_type_Pattern: Coding = {
  code: 'pattern',
  display: 'Pattern',
  system: 'http://hl7.org/fhir/discriminator-type',
};
const discriminator_type_Profile: Coding = {
  code: 'profile',
  display: 'Profile',
  system: 'http://hl7.org/fhir/discriminator-type',
};
const discriminator_type_Type: Coding = {
  code: 'type',
  display: 'Type',
  system: 'http://hl7.org/fhir/discriminator-type',
};
const discriminator_type_Value: Coding = {
  code: 'value',
  display: 'Value',
  system: 'http://hl7.org/fhir/discriminator-type',
};
/**
 * How an element value is interpreted when discrimination is evaluated.
 */
export const DiscriminatorType = {
  /**
   * The slices are differentiated by the presence or absence of the nominated element.
   */
  Exists: discriminator_type_Exists,
  /**
   * The slices have different values in the nominated element, as determined by testing them against the applicable ElementDefinition.pattern[x].
   */
  Pattern: discriminator_type_Pattern,
  /**
   * The slices are differentiated by conformance of the nominated element to a specified profile. Note that if the path specifies .resolve() then the profile is the target profile on the reference. In this case, validation by the possible profiles is required to differentiate the slices.
   */
  Profile: discriminator_type_Profile,
  /**
   * The slices are differentiated by type of the nominated element.
   */
  Type: discriminator_type_Type,
  /**
   * The slices have different values in the nominated element.
   */
  Value: discriminator_type_Value,
};
const DistanceUnits_Kilometers: Coding = {
  code: 'km',
  display: 'kilometers',
  system: 'http://unitsofmeasure.org',
};
const DistanceUnits_Meters: Coding = {
  code: 'm',
  display: 'meters',
  system: 'http://unitsofmeasure.org',
};
const DistanceUnits_Millimeters: Coding = {
  code: 'mm',
  display: 'millimeters',
  system: 'http://unitsofmeasure.org',
};
const DistanceUnits_Nanometers: Coding = {
  code: 'nm',
  display: 'nanometers',
  system: 'http://unitsofmeasure.org',
};
const DistanceUnits_Micrometers: Coding = {
  code: 'um',
  display: 'micrometers',
  system: 'http://unitsofmeasure.org',
};
/**
 * Unified Code for Units of Measure (UCUM). This value set includes common UCUM codes for units of distance
 */
export const DistanceUnits = {
  Kilometers: DistanceUnits_Kilometers,
  Meters: DistanceUnits_Meters,
  Millimeters: DistanceUnits_Millimeters,
  Nanometers: DistanceUnits_Nanometers,
  Micrometers: DistanceUnits_Micrometers,
};
const DocSectionCodes_VAL101543: Coding = {
  code: '10154-3',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL101576: Coding = {
  code: '10157-6',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL101600: Coding = {
  code: '10160-0',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL101642: Coding = {
  code: '10164-2',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL101832: Coding = {
  code: '10183-2',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL101840: Coding = {
  code: '10184-0',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL101873: Coding = {
  code: '10187-3',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL102103: Coding = {
  code: '10210-3',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL102160: Coding = {
  code: '10216-0',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL102186: Coding = {
  code: '10218-6',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL102228: Coding = {
  code: '10222-8',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL102236: Coding = {
  code: '10223-6',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL113290: Coding = {
  code: '11329-0',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL113480: Coding = {
  code: '11348-0',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL113696: Coding = {
  code: '11369-6',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL114934: Coding = {
  code: '11493-4',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL115352: Coding = {
  code: '11535-2',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL115378: Coding = {
  code: '11537-8',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL187765: Coding = {
  code: '18776-5',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL292995: Coding = {
  code: '29299-5',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL295451: Coding = {
  code: '29545-1',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL295493: Coding = {
  code: '29549-3',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL295543: Coding = {
  code: '29554-3',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL297622: Coding = {
  code: '29762-2',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL309542: Coding = {
  code: '30954-2',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL423442: Coding = {
  code: '42344-2',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL423467: Coding = {
  code: '42346-7',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL423483: Coding = {
  code: '42348-3',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL423491: Coding = {
  code: '42349-1',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL462408: Coding = {
  code: '46240-8',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL462416: Coding = {
  code: '46241-6',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL578526: Coding = {
  code: '57852-6',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL86488: Coding = {
  code: '8648-8',
  system: 'http://loinc.org',
};
const DocSectionCodes_VAL87163: Coding = {
  code: '8716-3',
  system: 'http://loinc.org',
};
/**
 * Document section codes (LOINC codes used in CCDA sections).
 */
export const DocSectionCodes = {
  VAL101543: DocSectionCodes_VAL101543,
  VAL101576: DocSectionCodes_VAL101576,
  VAL101600: DocSectionCodes_VAL101600,
  VAL101642: DocSectionCodes_VAL101642,
  VAL101832: DocSectionCodes_VAL101832,
  VAL101840: DocSectionCodes_VAL101840,
  VAL101873: DocSectionCodes_VAL101873,
  VAL102103: DocSectionCodes_VAL102103,
  VAL102160: DocSectionCodes_VAL102160,
  VAL102186: DocSectionCodes_VAL102186,
  VAL102228: DocSectionCodes_VAL102228,
  VAL102236: DocSectionCodes_VAL102236,
  VAL113290: DocSectionCodes_VAL113290,
  VAL113480: DocSectionCodes_VAL113480,
  VAL113696: DocSectionCodes_VAL113696,
  VAL114934: DocSectionCodes_VAL114934,
  VAL115352: DocSectionCodes_VAL115352,
  VAL115378: DocSectionCodes_VAL115378,
  VAL187765: DocSectionCodes_VAL187765,
  HospitalConsultationsDocument: C80DocTypecodes_HospitalConsultationsDocument,
  VAL292995: DocSectionCodes_VAL292995,
  VAL295451: DocSectionCodes_VAL295451,
  VAL295493: DocSectionCodes_VAL295493,
  VAL295543: DocSectionCodes_VAL295543,
  VAL297622: DocSectionCodes_VAL297622,
  VAL309542: DocSectionCodes_VAL309542,
  VAL423442: DocSectionCodes_VAL423442,
  VAL423467: DocSectionCodes_VAL423467,
  VAL423483: DocSectionCodes_VAL423483,
  VAL423491: DocSectionCodes_VAL423491,
  VAL462408: DocSectionCodes_VAL462408,
  VAL462416: DocSectionCodes_VAL462416,
  HistoryOfMedicalDeviceUse: C80DocTypecodes_HistoryOfMedicalDeviceUse,
  FunctionalStatusAssessmentNote: C80DocTypecodes_FunctionalStatusAssessmentNote,
  HistoryOfProceduresDocument: C80DocTypecodes_HistoryOfProceduresDocument,
  AllergiesAndAdverseReactionsDocument: C80DocTypecodes_AllergiesAndAdverseReactionsDocument,
  PaymentSourcesDocument: C80DocTypecodes_PaymentSourcesDocument,
  AssessmentNote: C80DocTypecodes_AssessmentNote,
  ComplicationsDocument: C80DocTypecodes_ComplicationsDocument,
  SurgicalOperationNoteImplantsNarrative: C80DocTypecodes_SurgicalOperationNoteImplantsNarrative,
  VAL578526: DocSectionCodes_VAL578526,
  ProcedureIndicationsNarrative: C80DocTypecodes_ProcedureIndicationsNarrative,
  PostprocedureDiagnosisNarrative: C80DocTypecodes_PostprocedureDiagnosisNarrative,
  ProcedureEstimatedBloodLossNarrative: C80DocTypecodes_ProcedureEstimatedBloodLossNarrative,
  ProcedureImplantsNarrative: C80DocTypecodes_ProcedureImplantsNarrative,
  PlannedProcedureNarrative: C80DocTypecodes_PlannedProcedureNarrative,
  ProcedureSpecimensTakenNarrative: C80DocTypecodes_ProcedureSpecimensTakenNarrative,
  ProcedureDispositionNarrative: C80DocTypecodes_ProcedureDispositionNarrative,
  ProcedureFindingsNarrative: C80DocTypecodes_ProcedureFindingsNarrative,
  ObjectiveNarrative: C80DocTypecodes_ObjectiveNarrative,
  SubjectiveNarrative: C80DocTypecodes_SubjectiveNarrative,
  InstructionsTextNarrative: C80DocTypecodes_InstructionsTextNarrative,
  VAL86488: DocSectionCodes_VAL86488,
  HospitalDischargeInstructions: C80DocTypecodes_HospitalDischargeInstructions,
  VAL87163: DocSectionCodes_VAL87163,
};
const DocumentClasscodes_RadiologyStudiesSet: Coding = {
  code: '18726-0',
  display: 'Radiology studies (set)',
  system: 'http://loinc.org',
};
const DocumentClasscodes_LaboratoryStudiesSet: Coding = {
  code: '26436-6',
  display: 'Laboratory Studies (set)',
  system: 'http://loinc.org',
};
const DocumentClasscodes_CardiologyStudiesSet: Coding = {
  code: '26441-6',
  display: 'Cardiology studies (set)',
  system: 'http://loinc.org',
};
const DocumentClasscodes_ObstetricalStudiesSet: Coding = {
  code: '26442-4',
  display: 'Obstetrical studies (set)',
  system: 'http://loinc.org',
};
const DocumentClasscodes_GastroenterologyEndoscopyStudiesSet: Coding = {
  code: '27895-2',
  display: 'Gastroenterology endoscopy studies (set)',
  system: 'http://loinc.org',
};
const DocumentClasscodes_PulmonaryStudiesSet: Coding = {
  code: '27896-0',
  display: 'Pulmonary studies (set)',
  system: 'http://loinc.org',
};
const DocumentClasscodes_NeuromuscularElectrophysiologyStudiesSet: Coding = {
  code: '27897-8',
  display: 'Neuromuscular electrophysiology studies (set)',
  system: 'http://loinc.org',
};
const DocumentClasscodes_PathologyStudiesSet: Coding = {
  code: '27898-6',
  display: 'Pathology studies (set)',
  system: 'http://loinc.org',
};
const DocumentClasscodes_OphthalmologyOptometryStudiesSet: Coding = {
  code: '28619-5',
  display: 'Ophthalmology/optometry studies (set)',
  system: 'http://loinc.org',
};
const DocumentClasscodes_MiscellaneousStudiesSet: Coding = {
  code: '28634-4',
  display: 'Miscellaneous studies (set)',
  system: 'http://loinc.org',
};
const DocumentClasscodes_TransferOfCareReferralNote: Coding = {
  code: '34140-4',
  display: 'Transfer of care referral note',
  system: 'http://loinc.org',
};
const DocumentClasscodes_GeneralSurgeryPreOperativeEvaluationAndManagementNote: Coding = {
  code: '34775-7',
  display: 'General surgery Pre-operative evaluation and management note',
  system: 'http://loinc.org',
};
const DocumentClasscodes_NonPatientCommunication: Coding = {
  code: '47049-2',
  display: 'Non-patient Communication',
  system: 'http://loinc.org',
};
/**
 * This is the code specifying the high-level kind of document (e.g. Prescription, Discharge Summary, Report, etc.). Note: Class code for documents comes from LOINC, and is based upon one of the following:The type of service described by the document. It is described at a very high level in Section 7.3 of the LOINC Manual. The type study performed. It was determined by identifying modalities for study reports. The section of the chart where the document is placed. It was determined from the SETs created for Claims Attachment requests.
 */
export const DocumentClasscodes = {
  VAL113696: DocSectionCodes_VAL113696,
  AnesthesiaRecords: C80DocTypecodes_AnesthesiaRecords,
  ChemotherapyRecords: C80DocTypecodes_ChemotherapyRecords,
  ConsultNote: C80DocTypecodes_ConsultNote,
  ProviderUnspecifiedOperationNote: C80DocTypecodes_ProviderUnspecifiedOperationNote,
  ProviderUnspecifiedProgressNote: C80DocTypecodes_ProviderUnspecifiedProgressNote,
  NurseryRecords: C80DocTypecodes_NurseryRecords,
  LaborAndDeliveryRecords: C80DocTypecodes_LaborAndDeliveryRecords,
  RadiologyStudiesSet: DocumentClasscodes_RadiologyStudiesSet,
  DiagnosticImagingStudy: C80DocTypecodes_DiagnosticImagingStudy,
  ProviderUnspecifiedTransferSummary: C80DocTypecodes_ProviderUnspecifiedTransferSummary,
  DischargeSummary: C80DocTypecodes_DischargeSummary,
  LaboratoryStudiesSet: DocumentClasscodes_LaboratoryStudiesSet,
  CardiologyStudiesSet: DocumentClasscodes_CardiologyStudiesSet,
  ObstetricalStudiesSet: DocumentClasscodes_ObstetricalStudiesSet,
  GastroenterologyEndoscopyStudiesSet: DocumentClasscodes_GastroenterologyEndoscopyStudiesSet,
  PulmonaryStudiesSet: DocumentClasscodes_PulmonaryStudiesSet,
  NeuromuscularElectrophysiologyStudiesSet: DocumentClasscodes_NeuromuscularElectrophysiologyStudiesSet,
  PathologyStudiesSet: DocumentClasscodes_PathologyStudiesSet,
  ProviderUnspecifiedProcedureNote: C80DocTypecodes_ProviderUnspecifiedProcedureNote,
  OphthalmologyOptometryStudiesSet: DocumentClasscodes_OphthalmologyOptometryStudiesSet,
  MiscellaneousStudiesSet: DocumentClasscodes_MiscellaneousStudiesSet,
  DialysisRecords: C80DocTypecodes_DialysisRecords,
  NeonatalIntensiveCareRecords: C80DocTypecodes_NeonatalIntensiveCareRecords,
  CriticalCareRecords: C80DocTypecodes_CriticalCareRecords,
  PerioperativeRecords: C80DocTypecodes_PerioperativeRecords,
  Note: C80DocTypecodes_Note,
  HistoryAndPhysicalNote: C80DocTypecodes_HistoryAndPhysicalNote,
  InterventionalProcedureNote: C80DocTypecodes_InterventionalProcedureNote,
  PathologyProcedureNote: C80DocTypecodes_PathologyProcedureNote,
  SummaryOfEpisodeNote: C80DocTypecodes_SummaryOfEpisodeNote,
  TransferOfCareReferralNote: DocumentClasscodes_TransferOfCareReferralNote,
  TelephoneEncounterNote: C80DocTypecodes_TelephoneEncounterNote,
  GeneralSurgeryPreOperativeEvaluationAndManagementNote: DocumentClasscodes_GeneralSurgeryPreOperativeEvaluationAndManagementNote,
  HospitalAdmissionHistoryAndPhysicalNote: C80DocTypecodes_HospitalAdmissionHistoryAndPhysicalNote,
  CounselingNote: C80DocTypecodes_CounselingNote,
  StudyReport: C80DocTypecodes_StudyReport,
  SummaryOfDeathNote: C80DocTypecodes_SummaryOfDeathNote,
  NonPatientCommunication: DocumentClasscodes_NonPatientCommunication,
  PersonalHealthMonitoringReportDocument: C80DocTypecodes_PersonalHealthMonitoringReportDocument,
  MedicationSummaryDocument: C80DocTypecodes_MedicationSummaryDocument,
  PlanOfCareNote: C80DocTypecodes_PlanOfCareNote,
  PrivacyPolicyAcknowledgmentDocument: C80DocTypecodes_PrivacyPolicyAcknowledgmentDocument,
  PrivacyPolicyOrganizationDocument: C80DocTypecodes_PrivacyPolicyOrganizationDocument,
  ReferralNote: C80DocTypecodes_ReferralNote,
};
const document_mode_Consumer: Coding = {
  code: 'consumer',
  display: 'Consumer',
  system: 'http://hl7.org/fhir/document-mode',
};
const document_mode_Producer: Coding = {
  code: 'producer',
  display: 'Producer',
  system: 'http://hl7.org/fhir/document-mode',
};
/**
 * Whether the application produces or consumes documents.
 */
export const DocumentMode = {
  /**
   * The application consumes documents of the specified type.
   */
  Consumer: document_mode_Consumer,
  /**
   * The application produces documents of the specified type.
   */
  Producer: document_mode_Producer,
};
const document_reference_status_Current: Coding = {
  code: 'current',
  display: 'Current',
  system: 'http://hl7.org/fhir/document-reference-status',
};
const document_reference_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/document-reference-status',
};
const document_reference_status_Superseded: Coding = {
  code: 'superseded',
  display: 'Superseded',
  system: 'http://hl7.org/fhir/document-reference-status',
};
/**
 * The status of the document reference.
 */
export const DocumentReferenceStatus = {
  /**
   * This is the current reference for this document.
   */
  Current: document_reference_status_Current,
  /**
   * This reference was created in error.
   */
  EnteredInError: document_reference_status_EnteredInError,
  /**
   * This reference has been superseded by another reference.
   */
  Superseded: document_reference_status_Superseded,
};
const document_relationship_type_Appends: Coding = {
  code: 'appends',
  display: 'Appends',
  system: 'http://hl7.org/fhir/document-relationship-type',
};
const document_relationship_type_Replaces: Coding = {
  code: 'replaces',
  display: 'Replaces',
  system: 'http://hl7.org/fhir/document-relationship-type',
};
const document_relationship_type_Signs: Coding = {
  code: 'signs',
  display: 'Signs',
  system: 'http://hl7.org/fhir/document-relationship-type',
};
const document_relationship_type_Transforms: Coding = {
  code: 'transforms',
  display: 'Transforms',
  system: 'http://hl7.org/fhir/document-relationship-type',
};
/**
 * The type of relationship between documents.
 */
export const DocumentRelationshipType = {
  /**
   * This document adds additional information to the target document.
   */
  Appends: document_relationship_type_Appends,
  /**
   * This document logically replaces or supersedes the target document.
   */
  Replaces: document_relationship_type_Replaces,
  /**
   * This document is a signature of the target document.
   */
  Signs: document_relationship_type_Signs,
  /**
   * This document was generated by transforming the target document (e.g. format or language conversion).
   */
  Transforms: document_relationship_type_Transforms,
};
const dose_rate_type_Calculated: Coding = {
  code: 'calculated',
  display: 'Calculated',
  system: 'http://terminology.hl7.org/CodeSystem/dose-rate-type',
};
const dose_rate_type_Ordered: Coding = {
  code: 'ordered',
  display: 'Ordered',
  system: 'http://terminology.hl7.org/CodeSystem/dose-rate-type',
};
/**
 * The kind of dose or rate specified.
 */
export const DoseRateType = {
  /**
   * The dose specified is calculated by the prescriber or the system.
   */
  Calculated: dose_rate_type_Calculated,
  /**
   * The dose specified is as ordered by the prescriber.
   */
  Ordered: dose_rate_type_Ordered,
};
const DurationUnits_Milliseconds: Coding = {
  code: 'ms',
  display: 'milliseconds',
  system: 'http://unitsofmeasure.org',
};
const DurationUnits_Seconds: Coding = {
  code: 's',
  display: 'seconds',
  system: 'http://unitsofmeasure.org',
};
/**
 * Unified Code for Units of Measure (UCUM). This value set includes all UCUM codes
 */
export const DurationUnits = {
  Years: AgeUnits_Years,
  Days: AgeUnits_Days,
  Hours: AgeUnits_Hours,
  Minutes: AgeUnits_Minutes,
  Months: AgeUnits_Months,
  Milliseconds: DurationUnits_Milliseconds,
  Seconds: DurationUnits_Seconds,
  Weeks: AgeUnits_Weeks,
};
const effect_estimate_type_AbsoluteRiskDifference: Coding = {
  code: 'absolute-ARD',
  display: 'absolute risk difference',
  system: 'http://terminology.hl7.org/CodeSystem/effect-estimate-type',
};
const effect_estimate_type_MeanDifference: Coding = {
  code: 'absolute-MeanDiff',
  display: 'mean difference',
  system: 'http://terminology.hl7.org/CodeSystem/effect-estimate-type',
};
const effect_estimate_type_MedianDifference: Coding = {
  code: 'absolute-MedianDiff',
  display: 'median difference',
  system: 'http://terminology.hl7.org/CodeSystem/effect-estimate-type',
};
const effect_estimate_type_StandardizedMeanDifference: Coding = {
  code: 'absolute-SMD',
  display: 'standardized mean difference',
  system: 'http://terminology.hl7.org/CodeSystem/effect-estimate-type',
};
const effect_estimate_type_HazardRatio: Coding = {
  code: 'relative-HR',
  display: 'hazard ratio',
  system: 'http://terminology.hl7.org/CodeSystem/effect-estimate-type',
};
const effect_estimate_type_OddsRatio: Coding = {
  code: 'relative-OR',
  display: 'odds ratio',
  system: 'http://terminology.hl7.org/CodeSystem/effect-estimate-type',
};
const effect_estimate_type_RelativeRisk: Coding = {
  code: 'relative-RR',
  display: 'relative risk',
  system: 'http://terminology.hl7.org/CodeSystem/effect-estimate-type',
};
/**
 * Whether the effect estimate is an absolute effect estimate (absolute difference) or a relative effect estimate (relative difference), and the specific type of effect estimate (eg relative risk or median difference).
 */
export const EffectEstimateType = {
  /**
   * absolute risk difference (a type of absolute effect estimate).
   */
  AbsoluteRiskDifference: effect_estimate_type_AbsoluteRiskDifference,
  /**
   * mean difference (a type of absolute effect estimate).
   */
  MeanDifference: effect_estimate_type_MeanDifference,
  /**
   * median difference (a type of absolute effect estimate).
   */
  MedianDifference: effect_estimate_type_MedianDifference,
  /**
   * standardized mean difference (a type of absolute effect estimate).
   */
  StandardizedMeanDifference: effect_estimate_type_StandardizedMeanDifference,
  /**
   * hazard ratio (a type of relative effect estimate).
   */
  HazardRatio: effect_estimate_type_HazardRatio,
  /**
   * odds ratio (a type of relative effect estimate).
   */
  OddsRatio: effect_estimate_type_OddsRatio,
  /**
   * relative risk (a type of relative effect estimate).
   */
  RelativeRisk: effect_estimate_type_RelativeRisk,
};
const eligibilityrequest_purpose_CoverageAuthRequirements: Coding = {
  code: 'auth-requirements',
  display: 'Coverage auth-requirements',
  system: 'http://hl7.org/fhir/eligibilityrequest-purpose',
};
const eligibilityrequest_purpose_CoverageBenefits: Coding = {
  code: 'benefits',
  display: 'Coverage benefits',
  system: 'http://hl7.org/fhir/eligibilityrequest-purpose',
};
const eligibilityrequest_purpose_CoverageDiscovery: Coding = {
  code: 'discovery',
  display: 'Coverage Discovery',
  system: 'http://hl7.org/fhir/eligibilityrequest-purpose',
};
const eligibilityrequest_purpose_CoverageValidation: Coding = {
  code: 'validation',
  display: 'Coverage Validation',
  system: 'http://hl7.org/fhir/eligibilityrequest-purpose',
};
/**
 * A code specifying the types of information being requested.
 */
export const EligibilityrequestPurpose = {
  /**
   * The prior authorization requirements for the listed, or discovered if specified, converages for the categories of service and/or specifed biling codes are requested.
   */
  CoverageAuthRequirements: eligibilityrequest_purpose_CoverageAuthRequirements,
  /**
   * The plan benefits and optionally benefits consumed  for the listed, or discovered if specified, converages are requested.
   */
  CoverageBenefits: eligibilityrequest_purpose_CoverageBenefits,
  /**
   * The insurer is requested to report on any coverages which they are aware of in addition to any specifed.
   */
  CoverageDiscovery: eligibilityrequest_purpose_CoverageDiscovery,
  /**
   * A check that the specified coverages are in-force is requested.
   */
  CoverageValidation: eligibilityrequest_purpose_CoverageValidation,
};
const eligibilityresponse_purpose_CoverageAuthRequirements: Coding = {
  code: 'auth-requirements',
  display: 'Coverage auth-requirements',
  system: 'http://hl7.org/fhir/eligibilityresponse-purpose',
};
const eligibilityresponse_purpose_CoverageBenefits: Coding = {
  code: 'benefits',
  display: 'Coverage benefits',
  system: 'http://hl7.org/fhir/eligibilityresponse-purpose',
};
const eligibilityresponse_purpose_CoverageDiscovery: Coding = {
  code: 'discovery',
  display: 'Coverage Discovery',
  system: 'http://hl7.org/fhir/eligibilityresponse-purpose',
};
const eligibilityresponse_purpose_CoverageValidation: Coding = {
  code: 'validation',
  display: 'Coverage Validation',
  system: 'http://hl7.org/fhir/eligibilityresponse-purpose',
};
/**
 * A code specifying the types of information being requested.
 */
export const EligibilityresponsePurpose = {
  /**
   * The prior authorization requirements for the listed, or discovered if specified, converages for the categories of service and/or specifed biling codes are requested.
   */
  CoverageAuthRequirements: eligibilityresponse_purpose_CoverageAuthRequirements,
  /**
   * The plan benefits and optionally benefits consumed  for the listed, or discovered if specified, converages are requested.
   */
  CoverageBenefits: eligibilityresponse_purpose_CoverageBenefits,
  /**
   * The insurer is requested to report on any coverages which they are aware of in addition to any specifed.
   */
  CoverageDiscovery: eligibilityresponse_purpose_CoverageDiscovery,
  /**
   * A check that the specified coverages are in-force is requested.
   */
  CoverageValidation: eligibilityresponse_purpose_CoverageValidation,
};
const encounter_admit_source_BornInHospital: Coding = {
  code: 'born',
  display: 'Born in hospital',
  system: 'http://terminology.hl7.org/CodeSystem/admit-source',
};
const encounter_admit_source_FromAccidentEmergencyDepartment: Coding = {
  code: 'emd',
  display: 'From accident/emergency department',
  system: 'http://terminology.hl7.org/CodeSystem/admit-source',
};
const encounter_admit_source_GeneralPractitionerReferral: Coding = {
  code: 'gp',
  display: 'General Practitioner referral',
  system: 'http://terminology.hl7.org/CodeSystem/admit-source',
};
const encounter_admit_source_TransferredFromOtherHospital: Coding = {
  code: 'hosp-trans',
  display: 'Transferred from other hospital',
  system: 'http://terminology.hl7.org/CodeSystem/admit-source',
};
const encounter_admit_source_MedicalPractitionerPhysicianReferral: Coding = {
  code: 'mp',
  display: 'Medical Practitioner/physician referral',
  system: 'http://terminology.hl7.org/CodeSystem/admit-source',
};
const encounter_admit_source_FromNursingHome: Coding = {
  code: 'nursing',
  display: 'From nursing home',
  system: 'http://terminology.hl7.org/CodeSystem/admit-source',
};
const encounter_admit_source_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/admit-source',
};
const encounter_admit_source_FromOutpatientDepartment: Coding = {
  code: 'outp',
  display: 'From outpatient department',
  system: 'http://terminology.hl7.org/CodeSystem/admit-source',
};
const encounter_admit_source_FromPsychiatricHospital: Coding = {
  code: 'psych',
  display: 'From psychiatric hospital',
  system: 'http://terminology.hl7.org/CodeSystem/admit-source',
};
const encounter_admit_source_FromRehabilitationFacility: Coding = {
  code: 'rehab',
  display: 'From rehabilitation facility',
  system: 'http://terminology.hl7.org/CodeSystem/admit-source',
};
/**
 * This value set defines a set of codes that can be used to indicate from where the patient came in.
 */
export const EncounterAdmitSource = {
  /**
   * The patient is a newborn and the encounter will track the baby related activities (as opposed to the Mothers encounter - that may be associated using the newborn encounters partof property)
   */
  BornInHospital: encounter_admit_source_BornInHospital,
  /**
   * The patient has been transferred from the emergency department within the hospital. This is typically used in the transition to an inpatient encounter
   */
  FromAccidentEmergencyDepartment: encounter_admit_source_FromAccidentEmergencyDepartment,
  /**
   * The patient has been admitted due to a referred from a General Practitioner.
   */
  GeneralPractitionerReferral: encounter_admit_source_GeneralPractitionerReferral,
  /**
   * The Patient has been transferred from another hospital for this encounter.
   */
  TransferredFromOtherHospital: encounter_admit_source_TransferredFromOtherHospital,
  /**
   * The patient has been admitted due to a referred from a Specialist (as opposed to a General Practitioner).
   */
  MedicalPractitionerPhysicianReferral: encounter_admit_source_MedicalPractitionerPhysicianReferral,
  /**
   * The patient has been transferred from a nursing home.
   */
  FromNursingHome: encounter_admit_source_FromNursingHome,
  /**
   * The patient has been admitted from a source otherwise not specified here.
   */
  Other: encounter_admit_source_Other,
  /**
   * The patient has been transferred from an outpatient department within the hospital.
   */
  FromOutpatientDepartment: encounter_admit_source_FromOutpatientDepartment,
  /**
   * The patient has been transferred from a psychiatric facility.
   */
  FromPsychiatricHospital: encounter_admit_source_FromPsychiatricHospital,
  /**
   * The patient has been transferred from a rehabilitation facility or clinic.
   */
  FromRehabilitationFacility: encounter_admit_source_FromRehabilitationFacility,
};
const encounter_diet_DairyFree: Coding = {
  code: 'dairy-free',
  display: 'Dairy Free',
  system: 'http://terminology.hl7.org/CodeSystem/diet',
};
const encounter_diet_GlutenFree: Coding = {
  code: 'gluten-free',
  display: 'Gluten Free',
  system: 'http://terminology.hl7.org/CodeSystem/diet',
};
const encounter_diet_Halal: Coding = {
  code: 'halal',
  display: 'Halal',
  system: 'http://terminology.hl7.org/CodeSystem/diet',
};
const encounter_diet_Kosher: Coding = {
  code: 'kosher',
  display: 'Kosher',
  system: 'http://terminology.hl7.org/CodeSystem/diet',
};
const encounter_diet_NutFree: Coding = {
  code: 'nut-free',
  display: 'Nut Free',
  system: 'http://terminology.hl7.org/CodeSystem/diet',
};
const encounter_diet_Vegan: Coding = {
  code: 'vegan',
  display: 'Vegan',
  system: 'http://terminology.hl7.org/CodeSystem/diet',
};
const encounter_diet_Vegetarian: Coding = {
  code: 'vegetarian',
  display: 'Vegetarian',
  system: 'http://terminology.hl7.org/CodeSystem/diet',
};
/**
 * This value set defines a set of codes that can be used to indicate dietary preferences or restrictions a patient may have.
 */
export const EncounterDiet = {
  /**
   * Excludes dairy products.
   */
  DairyFree: encounter_diet_DairyFree,
  /**
   * Excludes ingredients containing gluten.
   */
  GlutenFree: encounter_diet_GlutenFree,
  /**
   * Foods that conform to Islamic law.
   */
  Halal: encounter_diet_Halal,
  /**
   * Foods that conform to Jewish dietary law.
   */
  Kosher: encounter_diet_Kosher,
  /**
   * Excludes ingredients containing nuts.
   */
  NutFree: encounter_diet_NutFree,
  /**
   * Food without meat, poultry, seafood, eggs, dairy products and other animal-derived substances.
   */
  Vegan: encounter_diet_Vegan,
  /**
   * Food without meat, poultry or seafood.
   */
  Vegetarian: encounter_diet_Vegetarian,
};
const encounter_discharge_disposition_LeftAgainstAdvice: Coding = {
  code: 'aadvice',
  display: 'Left against advice',
  system: 'http://terminology.hl7.org/CodeSystem/discharge-disposition',
};
const encounter_discharge_disposition_AlternativeHome: Coding = {
  code: 'alt-home',
  display: 'Alternative home',
  system: 'http://terminology.hl7.org/CodeSystem/discharge-disposition',
};
const encounter_discharge_disposition_Expired: Coding = {
  code: 'exp',
  display: 'Expired',
  system: 'http://terminology.hl7.org/CodeSystem/discharge-disposition',
};
const encounter_discharge_disposition_Home: Coding = {
  code: 'home',
  display: 'Home',
  system: 'http://terminology.hl7.org/CodeSystem/discharge-disposition',
};
const encounter_discharge_disposition_Hospice: Coding = {
  code: 'hosp',
  display: 'Hospice',
  system: 'http://terminology.hl7.org/CodeSystem/discharge-disposition',
};
const encounter_discharge_disposition_LongTermCare: Coding = {
  code: 'long',
  display: 'Long-term care',
  system: 'http://terminology.hl7.org/CodeSystem/discharge-disposition',
};
const encounter_discharge_disposition_Other: Coding = {
  code: 'oth',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/discharge-disposition',
};
const encounter_discharge_disposition_OtherHealthcareFacility: Coding = {
  code: 'other-hcf',
  display: 'Other healthcare facility',
  system: 'http://terminology.hl7.org/CodeSystem/discharge-disposition',
};
const encounter_discharge_disposition_PsychiatricHospital: Coding = {
  code: 'psy',
  display: 'Psychiatric hospital',
  system: 'http://terminology.hl7.org/CodeSystem/discharge-disposition',
};
const encounter_discharge_disposition_Rehabilitation: Coding = {
  code: 'rehab',
  display: 'Rehabilitation',
  system: 'http://terminology.hl7.org/CodeSystem/discharge-disposition',
};
const encounter_discharge_disposition_SkilledNursingFacility: Coding = {
  code: 'snf',
  display: 'Skilled nursing facility',
  system: 'http://terminology.hl7.org/CodeSystem/discharge-disposition',
};
/**
 * This value set defines a set of codes that can be used to where the patient left the hospital.
 */
export const EncounterDischargeDisposition = {
  /**
   * The patient self discharged against medical advice.
   */
  LeftAgainstAdvice: encounter_discharge_disposition_LeftAgainstAdvice,
  /**
   * The patient was discharged and has indicated that they are going to return home afterwards, but not the patient's home - e.g. a family member's home.
   */
  AlternativeHome: encounter_discharge_disposition_AlternativeHome,
  /**
   * The patient has deceased during this encounter.
   */
  Expired: encounter_discharge_disposition_Expired,
  /**
   * The patient was dicharged and has indicated that they are going to return home afterwards.
   */
  Home: encounter_discharge_disposition_Home,
  /**
   * The patient has been discharged into palliative care.
   */
  Hospice: encounter_discharge_disposition_Hospice,
  /**
   * The patient has been discharged into long-term care where is likely to be monitored through an ongoing episode-of-care.
   */
  LongTermCare: encounter_discharge_disposition_LongTermCare,
  /**
   * The discharge disposition has not otherwise defined.
   */
  Other: encounter_discharge_disposition_Other,
  /**
   * The patient was transferred to another healthcare facility.
   */
  OtherHealthcareFacility: encounter_discharge_disposition_OtherHealthcareFacility,
  /**
   * The patient has been transferred to a psychiatric facility.
   */
  PsychiatricHospital: encounter_discharge_disposition_PsychiatricHospital,
  /**
   * The patient was discharged and is to receive post acute care rehabilitation services.
   */
  Rehabilitation: encounter_discharge_disposition_Rehabilitation,
  /**
   * The patient has been discharged to a skilled nursing facility for the patient to receive additional care.
   */
  SkilledNursingFacility: encounter_discharge_disposition_SkilledNursingFacility,
};
const encounter_location_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/encounter-location-status',
};
const encounter_location_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/encounter-location-status',
};
const encounter_location_status_Planned: Coding = {
  code: 'planned',
  display: 'Planned',
  system: 'http://hl7.org/fhir/encounter-location-status',
};
const encounter_location_status_Reserved: Coding = {
  code: 'reserved',
  display: 'Reserved',
  system: 'http://hl7.org/fhir/encounter-location-status',
};
/**
 * The status of the location.
 */
export const EncounterLocationStatus = {
  /**
   * The patient is currently at this location, or was between the period specified.
   * A system may update these records when the patient leaves the location to either reserved, or completed.
   */
  Active: encounter_location_status_Active,
  /**
   * The patient was at this location during the period specified.
   * Not to be used when the patient is currently at the location.
   */
  Completed: encounter_location_status_Completed,
  /**
   * The patient is planned to be moved to this location at some point in the future.
   */
  Planned: encounter_location_status_Planned,
  /**
   * This location is held empty for this patient.
   */
  Reserved: encounter_location_status_Reserved,
};
const v3_ParticipationType_Admitter: Coding = {
  code: 'ADM',
  display: 'admitter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Attender: Coding = {
  code: 'ATND',
  display: 'attender',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_CallbackContact: Coding = {
  code: 'CALLBCK',
  display: 'callback contact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Consultant: Coding = {
  code: 'CON',
  display: 'consultant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Discharger: Coding = {
  code: 'DIS',
  display: 'discharger',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const encounter_participant_type_Emergency: Coding = {
  code: 'emergency',
  display: 'Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/participant-type',
};
const v3_ParticipationType_Escort: Coding = {
  code: 'ESC',
  display: 'escort',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Participation: Coding = {
  code: 'PART',
  display: 'Participation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_PrimaryPerformer: Coding = {
  code: 'PPRF',
  display: 'primary performer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Referrer: Coding = {
  code: 'REF',
  display: 'referrer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_SecondaryPerformer: Coding = {
  code: 'SPRF',
  display: 'secondary performer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const encounter_participant_type_Translator: Coding = {
  code: 'translator',
  display: 'Translator',
  system: 'http://terminology.hl7.org/CodeSystem/participant-type',
};
/**
 * This value set defines a set of codes that can be used to indicate how an individual participates in an encounter.
 */
export const EncounterParticipantType = {
  /**
   * The practitioner who is responsible for admitting a patient to a patient encounter.
   */
  Admitter_v3_ParticipationType: v3_ParticipationType_Admitter,
  /**
   * The practitioner that has responsibility for overseeing a patient's care during a patient encounter.
   */
  Attender_v3_ParticipationType: v3_ParticipationType_Attender,
  /**
   * A person or organization who should be contacted for follow-up questions about the act in place of the author.
   */
  CallbackContact_v3_ParticipationType: v3_ParticipationType_CallbackContact,
  /**
   * An advisor participating in the service by performing evaluations and making recommendations.
   */
  Consultant_v3_ParticipationType: v3_ParticipationType_Consultant,
  /**
   * The practitioner who is responsible for the discharge of a patient from a patient encounter.
   */
  Discharger_v3_ParticipationType: v3_ParticipationType_Discharger,
  /**
   * A person to be contacted in case of an emergency during the encounter.
   */
  Emergency_encounter_participant_type: encounter_participant_type_Emergency,
  /**
   * Only with Transportation services.  A person who escorts the patient.
   */
  Escort_v3_ParticipationType: v3_ParticipationType_Escort,
  /**
   * Indicates that the target of the participation is involved in some manner in the act, but does not qualify how.
   */
  Participation_v3_ParticipationType: v3_ParticipationType_Participation,
  /**
   * The principal or primary performer of the act.
   */
  PrimaryPerformer_v3_ParticipationType: v3_ParticipationType_PrimaryPerformer,
  /**
   * A person having referred the subject of the service to the performer (referring physician).  Typically, a referring physician will receive a report.
   */
  Referrer_v3_ParticipationType: v3_ParticipationType_Referrer,
  /**
   * A person assisting in an act through his substantial presence and involvement   This includes: assistants, technicians, associates, or whatever the job titles may be.
   */
  SecondaryPerformer_v3_ParticipationType: v3_ParticipationType_SecondaryPerformer,
  /**
   * A translator who is facilitating communication with the patient during the encounter.
   */
  Translator_encounter_participant_type: encounter_participant_type_Translator,
};
const encounter_special_arrangements_AdditionalBedding: Coding = {
  code: 'add-bed',
  display: 'Additional bedding',
  system: 'http://terminology.hl7.org/CodeSystem/encounter-special-arrangements',
};
const encounter_special_arrangements_Attendant: Coding = {
  code: 'att',
  display: 'Attendant',
  system: 'http://terminology.hl7.org/CodeSystem/encounter-special-arrangements',
};
const encounter_special_arrangements_GuideDog: Coding = {
  code: 'dog',
  display: 'Guide dog',
  system: 'http://terminology.hl7.org/CodeSystem/encounter-special-arrangements',
};
const encounter_special_arrangements_Interpreter: Coding = {
  code: 'int',
  display: 'Interpreter',
  system: 'http://terminology.hl7.org/CodeSystem/encounter-special-arrangements',
};
const encounter_special_arrangements_Wheelchair: Coding = {
  code: 'wheel',
  display: 'Wheelchair',
  system: 'http://terminology.hl7.org/CodeSystem/encounter-special-arrangements',
};
/**
 * This value set defines a set of codes that can be used to indicate the kinds of special arrangements in place for a patients visit.
 */
export const EncounterSpecialArrangements = {
  /**
   * An additional bed made available for a person accompanying the patient, for example a parent accompanying a child.
   */
  AdditionalBedding: encounter_special_arrangements_AdditionalBedding,
  /**
   * A person who accompanies a patient to provide assistive services necessary for the patient's care during the encounter.
   */
  Attendant: encounter_special_arrangements_Attendant,
  /**
   * The patient has a guide dog and the location used for the encounter should be able to support the presence of the service animal.
   */
  GuideDog: encounter_special_arrangements_GuideDog,
  /**
   * The patient is not fluent in the local language and requires an interpreter to be available. Refer to the Patient.Language property for the type of interpreter required.
   */
  Interpreter: encounter_special_arrangements_Interpreter,
  /**
   * The patient requires a wheelchair to be made available for the encounter.
   */
  Wheelchair: encounter_special_arrangements_Wheelchair,
};
const v3_EncounterSpecialCourtesy_ExtendedCourtesy: Coding = {
  code: 'EXT',
  display: 'extended courtesy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EncounterSpecialCourtesy',
};
const v3_EncounterSpecialCourtesy_NormalCourtesy: Coding = {
  code: 'NRM',
  display: 'normal courtesy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EncounterSpecialCourtesy',
};
const v3_EncounterSpecialCourtesy_ProfessionalCourtesy: Coding = {
  code: 'PRF',
  display: 'professional courtesy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EncounterSpecialCourtesy',
};
const v3_EncounterSpecialCourtesy_Staff: Coding = {
  code: 'STF',
  display: 'staff',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EncounterSpecialCourtesy',
};
const v3_NullFlavor_Unknown: Coding = {
  code: 'UNK',
  display: 'unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_EncounterSpecialCourtesy_VeryImportantPerson: Coding = {
  code: 'VIP',
  display: 'very important person',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EncounterSpecialCourtesy',
};
/**
 * This value set defines a set of codes that can be used to indicate special courtesies provided to the patient.
 */
export const EncounterSpecialCourtesy = {
  /**
   * extended courtesy
   */
  ExtendedCourtesy_v3_EncounterSpecialCourtesy: v3_EncounterSpecialCourtesy_ExtendedCourtesy,
  /**
   * normal courtesy
   */
  NormalCourtesy_v3_EncounterSpecialCourtesy: v3_EncounterSpecialCourtesy_NormalCourtesy,
  /**
   * professional courtesy
   */
  ProfessionalCourtesy_v3_EncounterSpecialCourtesy: v3_EncounterSpecialCourtesy_ProfessionalCourtesy,
  /**
   * Courtesies extended to the staff of the entity providing service.
   */
  Staff_v3_EncounterSpecialCourtesy: v3_EncounterSpecialCourtesy_Staff,
  /**
   * Description:A proper value is applicable, but not known.
   *
   *
   *                            Usage Notes: This means the actual value is not known.  If the only thing that is unknown is how to properly express the value in the necessary constraints (value set, datatype, etc.), then the OTH or UNC flavor should be used.  No properties should be included for a datatype with this property unless:
   *
   *
   *                            Those properties themselves directly translate to a semantic of "unknown".  (E.g. a local code sent as a translation that conveys 'unknown')
   *                            Those properties further qualify the nature of what is unknown.  (E.g. specifying a use code of "H" and a URL prefix of "tel:" to convey that it is the home phone number that is unknown.)
   */
  Unknown_v3_NullFlavor: v3_NullFlavor_Unknown,
  /**
   * very important person
   */
  VeryImportantPerson_v3_EncounterSpecialCourtesy: v3_EncounterSpecialCourtesy_VeryImportantPerson,
};
const encounter_status_Arrived: Coding = {
  code: 'arrived',
  display: 'Arrived',
  system: 'http://hl7.org/fhir/encounter-status',
};
const encounter_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/encounter-status',
};
const encounter_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/encounter-status',
};
const encounter_status_Finished: Coding = {
  code: 'finished',
  display: 'Finished',
  system: 'http://hl7.org/fhir/encounter-status',
};
const encounter_status_InProgress: Coding = {
  code: 'in-progress',
  display: 'In Progress',
  system: 'http://hl7.org/fhir/encounter-status',
};
const encounter_status_OnLeave: Coding = {
  code: 'onleave',
  display: 'On Leave',
  system: 'http://hl7.org/fhir/encounter-status',
};
const encounter_status_Planned: Coding = {
  code: 'planned',
  display: 'Planned',
  system: 'http://hl7.org/fhir/encounter-status',
};
const encounter_status_Triaged: Coding = {
  code: 'triaged',
  display: 'Triaged',
  system: 'http://hl7.org/fhir/encounter-status',
};
const encounter_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/encounter-status',
};
/**
 * Current state of the encounter.
 */
export const EncounterStatus = {
  /**
   * The Patient is present for the encounter, however is not currently meeting with a practitioner.
   */
  Arrived: encounter_status_Arrived,
  /**
   * The Encounter has ended before it has begun.
   */
  Cancelled: encounter_status_Cancelled,
  /**
   * This instance should not have been part of this patient's medical record.
   */
  EnteredInError: encounter_status_EnteredInError,
  /**
   * The Encounter has ended.
   */
  Finished: encounter_status_Finished,
  /**
   * The Encounter has begun and the patient is present / the practitioner and the patient are meeting.
   */
  InProgress: encounter_status_InProgress,
  /**
   * The Encounter has begun, but the patient is temporarily on leave.
   */
  OnLeave: encounter_status_OnLeave,
  /**
   * The Encounter has not yet started.
   */
  Planned: encounter_status_Planned,
  /**
   * The patient has been assessed for the priority of their treatment based on the severity of their condition.
   */
  Triaged: encounter_status_Triaged,
  /**
   * The encounter status is unknown. Note that "unknown" is a value of last resort and every attempt should be made to provide a meaningful value other than "unknown".
   */
  Unknown: encounter_status_Unknown,
};
const encounter_type_AnnualDiabetesMellitusScreening: Coding = {
  code: 'ADMS',
  display: 'Annual diabetes mellitus screening',
  system: 'http://terminology.hl7.org/CodeSystem/encounter-type',
};
const encounter_type_BoneDrillingBoneMarrowPunctionInClinic: Coding = {
  code: 'BD/BM-clin',
  display: 'Bone drilling/bone marrow punction in clinic',
  system: 'http://terminology.hl7.org/CodeSystem/encounter-type',
};
const encounter_type_InfantColonScreening60Minutes: Coding = {
  code: 'CCS60',
  display: 'Infant colon screening - 60 minutes',
  system: 'http://terminology.hl7.org/CodeSystem/encounter-type',
};
const encounter_type_OutpatientKenacortInjection: Coding = {
  code: 'OKI',
  display: 'Outpatient Kenacort injection',
  system: 'http://terminology.hl7.org/CodeSystem/encounter-type',
};
/**
 * This example value set defines a set of codes that can be used to indicate the type of encounter: a specific code indicating type of service provided.
 */
export const EncounterType = {
  AnnualDiabetesMellitusScreening: encounter_type_AnnualDiabetesMellitusScreening,
  BoneDrillingBoneMarrowPunctionInClinic: encounter_type_BoneDrillingBoneMarrowPunctionInClinic,
  InfantColonScreening60Minutes: encounter_type_InfantColonScreening60Minutes,
  OutpatientKenacortInjection: encounter_type_OutpatientKenacortInjection,
};
const endpoint_connection_type_DICOMQIDORS: Coding = {
  code: 'dicom-qido-rs',
  display: 'DICOM QIDO-RS',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
const endpoint_connection_type_DICOMSTOWRS: Coding = {
  code: 'dicom-stow-rs',
  display: 'DICOM STOW-RS',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
const endpoint_connection_type_DICOMWADORS: Coding = {
  code: 'dicom-wado-rs',
  display: 'DICOM WADO-RS',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
const endpoint_connection_type_DICOMWADOURI: Coding = {
  code: 'dicom-wado-uri',
  display: 'DICOM WADO-URI',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
const endpoint_connection_type_DirectProject: Coding = {
  code: 'direct-project',
  display: 'Direct Project',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
const endpoint_connection_type_HL7FHIRMessaging: Coding = {
  code: 'hl7-fhir-msg',
  display: 'HL7 FHIR Messaging',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
const endpoint_connection_type_HL7FHIR: Coding = {
  code: 'hl7-fhir-rest',
  display: 'HL7 FHIR',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
const endpoint_connection_type_HL7V2MLLP: Coding = {
  code: 'hl7v2-mllp',
  display: 'HL7 v2 MLLP',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
const endpoint_connection_type_IHEIID: Coding = {
  code: 'ihe-iid',
  display: 'IHE IID',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
const endpoint_connection_type_IHEXCA: Coding = {
  code: 'ihe-xca',
  display: 'IHE XCA',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
const endpoint_connection_type_IHEXCPD: Coding = {
  code: 'ihe-xcpd',
  display: 'IHE XCPD',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
const endpoint_connection_type_IHEXDR: Coding = {
  code: 'ihe-xdr',
  display: 'IHE XDR',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
const endpoint_connection_type_IHEXDS: Coding = {
  code: 'ihe-xds',
  display: 'IHE XDS',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
const endpoint_connection_type_SecureEmail: Coding = {
  code: 'secure-email',
  display: 'Secure email',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type',
};
/**
 * This is an example value set defined by the FHIR project, that could be used to represent possible connection type profile values.
 */
export const EndpointConnectionType = {
  /**
   * DICOMweb RESTful Image query - http://dicom.nema.org/medical/dicom/current/output/chtml/part18/sect_6.7.html
   */
  DICOMQIDORS: endpoint_connection_type_DICOMQIDORS,
  /**
   * DICOMweb RESTful image sending and storage - http://dicom.nema.org/medical/dicom/current/output/chtml/part18/sect_6.6.html
   */
  DICOMSTOWRS: endpoint_connection_type_DICOMSTOWRS,
  /**
   * DICOMweb RESTful Image Retrieve - http://dicom.nema.org/medical/dicom/current/output/chtml/part18/sect_6.5.html
   */
  DICOMWADORS: endpoint_connection_type_DICOMWADORS,
  /**
   * DICOMweb Image Retrieve - http://dicom.nema.org/dicom/2013/output/chtml/part18/sect_6.3.html
   */
  DICOMWADOURI: endpoint_connection_type_DICOMWADOURI,
  /**
   * Direct Project information - http://wiki.directproject.org/
   */
  DirectProject: endpoint_connection_type_DirectProject,
  /**
   * Use the servers FHIR Messaging interface. Details can be found on the messaging.html page in the FHIR Specification. The FHIR server's base address is specified in the Endpoint.address property.
   */
  HL7FHIRMessaging: endpoint_connection_type_HL7FHIRMessaging,
  /**
   * Interact with the server interface using FHIR's RESTful interface. For details on its version/capabilities you should connect the value in Endpoint.address and retrieve the FHIR CapabilityStatement.
   */
  HL7FHIR: endpoint_connection_type_HL7FHIR,
  /**
   * HL7v2 messages over an LLP TCP connection
   */
  HL7V2MLLP: endpoint_connection_type_HL7V2MLLP,
  /**
   * IHE Invoke Image Display (IID) - http://wiki.ihe.net/index.php/Invoke_Image_Display
   */
  IHEIID: endpoint_connection_type_IHEIID,
  /**
   * IHE Cross Community Access Profile (XCA) - http://wiki.ihe.net/index.php/Cross-Community_Access
   */
  IHEXCA: endpoint_connection_type_IHEXCA,
  /**
   * IHE Cross Community Patient Discovery Profile (XCPD) - http://wiki.ihe.net/index.php/Cross-Community_Patient_Discovery
   */
  IHEXCPD: endpoint_connection_type_IHEXCPD,
  /**
   * IHE Cross-Enterprise Document Reliable Exchange (XDR) - http://wiki.ihe.net/index.php/Cross-enterprise_Document_Reliable_Interchange
   */
  IHEXDR: endpoint_connection_type_IHEXDR,
  /**
   * IHE Cross-Enterprise Document Sharing (XDS) - http://wiki.ihe.net/index.php/Cross-Enterprise_Document_Sharing
   */
  IHEXDS: endpoint_connection_type_IHEXDS,
  /**
   * Email delivery using a digital certificate to encrypt the content using the public key, receiver must have the private key to decrypt the content
   */
  SecureEmail: endpoint_connection_type_SecureEmail,
};
const endpoint_payload_type_Any: Coding = {
  code: 'any',
  display: 'Any',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-payload-type',
};
const endpoint_payload_type_None: Coding = {
  code: 'none',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/endpoint-payload-type',
};
const EndpointPayloadType_ForDocumentsFollowingCCDAConstraintsUsingANonStructuredBody: Coding = {
  code: 'urn:hl7-org:sdwg:ccda-nonXMLBody:1.1',
  display: 'For documents following C-CDA constraints using a non structured body. ',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_ForDocumentsFollowingCCDAConstraintsUsingAStructuredBody: Coding = {
  code: 'urn:hl7-org:sdwg:ccda-structuredBody:1.1',
  display: 'For documents following C-CDA constraints using a structured body. ',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_CardiologyCRC: Coding = {
  code: 'urn:ihe:card:CRC:2012',
  display: 'Cardiology CRC',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_CardiologyEPRCIE: Coding = {
  code: 'urn:ihe:card:EPRC-IE:2014',
  display: 'Cardiology EPRC-IE',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_CardiacImagingReport: Coding = {
  code: 'urn:ihe:card:imaging:2011',
  display: 'Cardiac Imaging Report',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_DentalCDA: Coding = {
  code: 'urn:ihe:dent:CDA:ImagingReportStructuredHeadings:2013',
  display: 'Dental CDA',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_DentalPDF: Coding = {
  code: 'urn:ihe:dent:PDF',
  display: 'Dental PDF',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_DentalText: Coding = {
  code: 'urn:ihe:dent:TEXT',
  display: 'Dental Text',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_BasicPatientPrivacyConsents: Coding = {
  code: 'urn:ihe:iti:bppc:2007',
  display: 'Basic Patient Privacy Consents',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_BasicPatientPrivacyConsentsWithScannedDocument: Coding = {
  code: 'urn:ihe:iti:bppc-sd:2007',
  display: 'Basic Patient Privacy Consents with Scanned Document',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_DSGDetachedDocument: Coding = {
  code: 'urn:ihe:iti:dsg:detached:2014',
  display: 'DSG Detached Document',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_DSGEnvelopingDocument: Coding = {
  code: 'urn:ihe:iti:dsg:enveloping:2014',
  display: 'DSG Enveloping Document',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PDFEmbeddedInCDAPerXDSSDProfile: Coding = {
  code: 'urn:ihe:iti:xds-sd:pdf:2008',
  display: 'PDF embedded in CDA per XDS-SD profile',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_TextEmbeddedInCDAPerXDSSDProfile: Coding = {
  code: 'urn:ihe:iti:xds-sd:text:2008',
  display: 'Text embedded in CDA per XDS-SD profile',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_XDWWorkflowDocument: Coding = {
  code: 'urn:ihe:iti:xdw:2011:workflowDoc',
  display: 'XDW Workflow Document',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_CDALaboratoryReport: Coding = {
  code: 'urn:ihe:lab:xd-lab:2008',
  display: 'CDA Laboratory Report',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportAll: Coding = {
  code: 'urn:ihe:pat:apsr:all:2010',
  display: 'Anatomic Pathology Structured Report All',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerAll: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:all:2010',
  display: 'Anatomic Pathology Structured Report Cancer All',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerBreast: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:breast:2010',
  display: 'Anatomic Pathology Structured Report Cancer Breast',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerCervix: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:cervix:2010',
  display: 'Anatomic Pathology Structured Report Cancer Cervix',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerColon: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:colon:2010',
  display: 'Anatomic Pathology Structured Report Cancer Colon',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerEndometrium: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:endometrium:2010',
  display: 'Anatomic Pathology Structured Report Cancer Endometrium',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerEsophagus: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:esophagus: 2010',
  display: 'Anatomic Pathology Structured Report Cancer Esophagus',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerKidney: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:kidney:2010',
  display: 'Anatomic Pathology Structured Report Cancer Kidney',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerLarynx: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:larynx:2010',
  display: 'Anatomic Pathology Structured Report Cancer Larynx',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerLipOralCavity: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:lip_oral_cavity:2010',
  display: 'Anatomic Pathology Structured Report Cancer Lip Oral Cavity',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerLiver: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:liver:2010',
  display: 'Anatomic Pathology Structured Report Cancer Liver',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerLung: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:lung:2010',
  display: 'Anatomic Pathology Structured Report Cancer Lung',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerOvary: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:ovary:2010',
  display: 'Anatomic Pathology Structured Report Cancer Ovary',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerPancreas: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:pancreas: 2010',
  display: 'Anatomic Pathology Structured Report Cancer Pancreas',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerPharynx: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:pharynx:2010',
  display: 'Anatomic Pathology Structured Report Cancer Pharynx',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerProstate: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:prostate:2010',
  display: 'Anatomic Pathology Structured Report Cancer Prostate',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerSalivaryGland: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:salivary_gland:2010',
  display: 'Anatomic Pathology Structured Report Cancer Salivary Gland',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerSkin: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:skin:2010',
  display: 'Anatomic Pathology Structured Report Cancer Skin',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerStomach: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:stomach: 2010',
  display: 'Anatomic Pathology Structured Report Cancer Stomach',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerTestis: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:testis:2010',
  display: 'Anatomic Pathology Structured Report Cancer Testis',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerThyroid: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:thyroid:2010',
  display: 'Anatomic Pathology Structured Report Cancer Thyroid',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AnatomicPathologyStructuredReportCancerUrinaryBladder: Coding = {
  code: 'urn:ihe:pat:apsr:cancer:urinary_bladder:2010',
  display: 'Anatomic Pathology Structured Report Cancer Urinary Bladder',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AntepartumRecordAPREducation: Coding = {
  code: 'urn:ihe:pcc:apr:edu:2008',
  display: 'Antepartum Record (APR) - Education',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AntepartumRecordAPRHistoryAndPhysical: Coding = {
  code: 'urn:ihe:pcc:apr:handp:2008',
  display: 'Antepartum Record (APR) - History and Physical',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_AntepartumRecordAPRLaboratory: Coding = {
  code: 'urn:ihe:pcc:apr:lab:2008',
  display: 'Antepartum Record (APR) - Laboratory',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_IHEAntepartumSummary: Coding = {
  code: 'urn:ihe:pcc:aps:2007',
  display: 'IHE Antepartum Summary',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_CareManagementCM: Coding = {
  code: 'urn:ihe:pcc:cm:2008',
  display: 'Care Management (CM)',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_CancerRegistryContentCRC: Coding = {
  code: 'urn:ihe:pcc:crc:2008',
  display: 'Cancer Registry Content (CRC)',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PCCCTN: Coding = {
  code: 'urn:ihe:pcc:ctn:2007',
  display: 'PCC CTN',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_EmergencyDepartmentEncounterSummaryEDES: Coding = {
  code: 'urn:ihe:pcc:edes:2007',
  display: 'Emergency Department Encounter Summary (EDES)',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PCCEDPN: Coding = {
  code: 'urn:ihe:pcc:edpn:2007',
  display: 'PCC EDPN',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_EmergencyDepartmentReferralEDR: Coding = {
  code: 'urn:ihe:pcc:edr:2007',
  display: 'Emergency Department Referral (EDR)',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PCCETS: Coding = {
  code: 'urn:ihe:pcc:ets:2011',
  display: 'PCC ETS',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_HistoryAndPhysicalSpecification: Coding = {
  code: 'urn:ihe:pcc:handp:2008',
  display: 'History and Physical Specification',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PCCHP: Coding = {
  code: 'urn:ihe:pcc:hp:2008',
  display: 'PCC HP',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_ImmunizationContentIC: Coding = {
  code: 'urn:ihe:pcc:ic:2009',
  display: 'Immunization Content (IC)',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_ImmunizationRegistryContentIRC: Coding = {
  code: 'urn:ihe:pcc:irc:2008',
  display: 'Immunization Registry Content (IRC)',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PCCITS: Coding = {
  code: 'urn:ihe:pcc:its:2011',
  display: 'PCC ITS',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PCCLDHP: Coding = {
  code: 'urn:ihe:pcc:ldhp:2009',
  display: 'PCC LDHP',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PCCLDS: Coding = {
  code: 'urn:ihe:pcc:lds:2009',
  display: 'PCC LDS',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PCCMDS: Coding = {
  code: 'urn:ihe:pcc:mds:2009',
  display: 'PCC MDS',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PCCNDS: Coding = {
  code: 'urn:ihe:pcc:nds:2010',
  display: 'PCC NDS',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PCCNN: Coding = {
  code: 'urn:ihe:pcc:nn:2007',
  display: 'PCC NN',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PCCPPVS: Coding = {
  code: 'urn:ihe:pcc:ppvs:2010',
  display: 'PCC PPVS',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PCCTN: Coding = {
  code: 'urn:ihe:pcc:tn:2007',
  display: 'PCC TN',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PCCTRS: Coding = {
  code: 'urn:ihe:pcc:trs:2011',
  display: 'PCC TRS',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_XDSMedicalSummaries: Coding = {
  code: 'urn:ihe:pcc:xds-ms:2007',
  display: 'XDS Medical Summaries',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_HL7CCDDocument: Coding = {
  code: 'urn:ihe:pcc:xphr:2007',
  display: 'HL7 CCD Document',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PharmacyDIS: Coding = {
  code: 'urn:ihe:pharm:dis:2010',
  display: 'Pharmacy DIS',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PharmacyPADV: Coding = {
  code: 'urn:ihe:pharm:padv:2010',
  display: 'Pharmacy PADV',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PharmacyPML: Coding = {
  code: 'urn:ihe:pharm:pml:2013',
  display: 'Pharmacy PML',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_PharmacyPre: Coding = {
  code: 'urn:ihe:pharm:pre:2010',
  display: 'Pharmacy Pre',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_RadiologyXDSIStructuredCDA: Coding = {
  code: 'urn:ihe:rad:CDA:ImagingReportStructuredHeadings:2013',
  display: 'Radiology XDS-I Structured CDA',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_RadiologyXDSIPDF: Coding = {
  code: 'urn:ihe:rad:PDF',
  display: 'Radiology XDS-I PDF',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
const EndpointPayloadType_RadiologyXDSIText: Coding = {
  code: 'urn:ihe:rad:TEXT',
  display: 'Radiology XDS-I Text',
  system: 'urn:oid:1.3.6.1.4.1.19376.1.2.3',
};
/**
 * This is an example value set defined by the FHIR project, that could be used to represent possible payload document types.
 */
export const EndpointPayloadType = {
  /**
   * Any payload type can be used with this endpoint, it is either a payload agnostic infrastructure (such as a storage repository), or some other type of endpoint where payload considerations are internally handled, and not available
   */
  Any: endpoint_payload_type_Any,
  /**
   * This endpoint does not require any content to be sent; simply connecting to the endpoint is enough notification. This can be used as a 'ping' to wakeup a service to retrieve content, which could be to ensure security considerations are correctly handled
   */
  None: endpoint_payload_type_None,
  ForDocumentsFollowingCCDAConstraintsUsingANonStructuredBody: EndpointPayloadType_ForDocumentsFollowingCCDAConstraintsUsingANonStructuredBody,
  ForDocumentsFollowingCCDAConstraintsUsingAStructuredBody: EndpointPayloadType_ForDocumentsFollowingCCDAConstraintsUsingAStructuredBody,
  CardiologyCRC: EndpointPayloadType_CardiologyCRC,
  CardiologyEPRCIE: EndpointPayloadType_CardiologyEPRCIE,
  CardiacImagingReport: EndpointPayloadType_CardiacImagingReport,
  DentalCDA: EndpointPayloadType_DentalCDA,
  DentalPDF: EndpointPayloadType_DentalPDF,
  DentalText: EndpointPayloadType_DentalText,
  BasicPatientPrivacyConsents: EndpointPayloadType_BasicPatientPrivacyConsents,
  BasicPatientPrivacyConsentsWithScannedDocument: EndpointPayloadType_BasicPatientPrivacyConsentsWithScannedDocument,
  DSGDetachedDocument: EndpointPayloadType_DSGDetachedDocument,
  DSGEnvelopingDocument: EndpointPayloadType_DSGEnvelopingDocument,
  PDFEmbeddedInCDAPerXDSSDProfile: EndpointPayloadType_PDFEmbeddedInCDAPerXDSSDProfile,
  TextEmbeddedInCDAPerXDSSDProfile: EndpointPayloadType_TextEmbeddedInCDAPerXDSSDProfile,
  XDWWorkflowDocument: EndpointPayloadType_XDWWorkflowDocument,
  CDALaboratoryReport: EndpointPayloadType_CDALaboratoryReport,
  AnatomicPathologyStructuredReportAll: EndpointPayloadType_AnatomicPathologyStructuredReportAll,
  AnatomicPathologyStructuredReportCancerAll: EndpointPayloadType_AnatomicPathologyStructuredReportCancerAll,
  AnatomicPathologyStructuredReportCancerBreast: EndpointPayloadType_AnatomicPathologyStructuredReportCancerBreast,
  AnatomicPathologyStructuredReportCancerCervix: EndpointPayloadType_AnatomicPathologyStructuredReportCancerCervix,
  AnatomicPathologyStructuredReportCancerColon: EndpointPayloadType_AnatomicPathologyStructuredReportCancerColon,
  AnatomicPathologyStructuredReportCancerEndometrium: EndpointPayloadType_AnatomicPathologyStructuredReportCancerEndometrium,
  AnatomicPathologyStructuredReportCancerEsophagus: EndpointPayloadType_AnatomicPathologyStructuredReportCancerEsophagus,
  AnatomicPathologyStructuredReportCancerKidney: EndpointPayloadType_AnatomicPathologyStructuredReportCancerKidney,
  AnatomicPathologyStructuredReportCancerLarynx: EndpointPayloadType_AnatomicPathologyStructuredReportCancerLarynx,
  AnatomicPathologyStructuredReportCancerLipOralCavity: EndpointPayloadType_AnatomicPathologyStructuredReportCancerLipOralCavity,
  AnatomicPathologyStructuredReportCancerLiver: EndpointPayloadType_AnatomicPathologyStructuredReportCancerLiver,
  AnatomicPathologyStructuredReportCancerLung: EndpointPayloadType_AnatomicPathologyStructuredReportCancerLung,
  AnatomicPathologyStructuredReportCancerOvary: EndpointPayloadType_AnatomicPathologyStructuredReportCancerOvary,
  AnatomicPathologyStructuredReportCancerPancreas: EndpointPayloadType_AnatomicPathologyStructuredReportCancerPancreas,
  AnatomicPathologyStructuredReportCancerPharynx: EndpointPayloadType_AnatomicPathologyStructuredReportCancerPharynx,
  AnatomicPathologyStructuredReportCancerProstate: EndpointPayloadType_AnatomicPathologyStructuredReportCancerProstate,
  AnatomicPathologyStructuredReportCancerSalivaryGland: EndpointPayloadType_AnatomicPathologyStructuredReportCancerSalivaryGland,
  AnatomicPathologyStructuredReportCancerSkin: EndpointPayloadType_AnatomicPathologyStructuredReportCancerSkin,
  AnatomicPathologyStructuredReportCancerStomach: EndpointPayloadType_AnatomicPathologyStructuredReportCancerStomach,
  AnatomicPathologyStructuredReportCancerTestis: EndpointPayloadType_AnatomicPathologyStructuredReportCancerTestis,
  AnatomicPathologyStructuredReportCancerThyroid: EndpointPayloadType_AnatomicPathologyStructuredReportCancerThyroid,
  AnatomicPathologyStructuredReportCancerUrinaryBladder: EndpointPayloadType_AnatomicPathologyStructuredReportCancerUrinaryBladder,
  AntepartumRecordAPREducation: EndpointPayloadType_AntepartumRecordAPREducation,
  AntepartumRecordAPRHistoryAndPhysical: EndpointPayloadType_AntepartumRecordAPRHistoryAndPhysical,
  AntepartumRecordAPRLaboratory: EndpointPayloadType_AntepartumRecordAPRLaboratory,
  IHEAntepartumSummary: EndpointPayloadType_IHEAntepartumSummary,
  CareManagementCM: EndpointPayloadType_CareManagementCM,
  CancerRegistryContentCRC: EndpointPayloadType_CancerRegistryContentCRC,
  PCCCTN: EndpointPayloadType_PCCCTN,
  EmergencyDepartmentEncounterSummaryEDES: EndpointPayloadType_EmergencyDepartmentEncounterSummaryEDES,
  PCCEDPN: EndpointPayloadType_PCCEDPN,
  EmergencyDepartmentReferralEDR: EndpointPayloadType_EmergencyDepartmentReferralEDR,
  PCCETS: EndpointPayloadType_PCCETS,
  HistoryAndPhysicalSpecification: EndpointPayloadType_HistoryAndPhysicalSpecification,
  PCCHP: EndpointPayloadType_PCCHP,
  ImmunizationContentIC: EndpointPayloadType_ImmunizationContentIC,
  ImmunizationRegistryContentIRC: EndpointPayloadType_ImmunizationRegistryContentIRC,
  PCCITS: EndpointPayloadType_PCCITS,
  PCCLDHP: EndpointPayloadType_PCCLDHP,
  PCCLDS: EndpointPayloadType_PCCLDS,
  PCCMDS: EndpointPayloadType_PCCMDS,
  PCCNDS: EndpointPayloadType_PCCNDS,
  PCCNN: EndpointPayloadType_PCCNN,
  PCCPPVS: EndpointPayloadType_PCCPPVS,
  PCCTN: EndpointPayloadType_PCCTN,
  PCCTRS: EndpointPayloadType_PCCTRS,
  XDSMedicalSummaries: EndpointPayloadType_XDSMedicalSummaries,
  HL7CCDDocument: EndpointPayloadType_HL7CCDDocument,
  PharmacyDIS: EndpointPayloadType_PharmacyDIS,
  PharmacyPADV: EndpointPayloadType_PharmacyPADV,
  PharmacyPML: EndpointPayloadType_PharmacyPML,
  PharmacyPre: EndpointPayloadType_PharmacyPre,
  RadiologyXDSIStructuredCDA: EndpointPayloadType_RadiologyXDSIStructuredCDA,
  RadiologyXDSIPDF: EndpointPayloadType_RadiologyXDSIPDF,
  RadiologyXDSIText: EndpointPayloadType_RadiologyXDSIText,
};
const endpoint_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/endpoint-status',
};
const endpoint_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in error',
  system: 'http://hl7.org/fhir/endpoint-status',
};
const endpoint_status_Error: Coding = {
  code: 'error',
  display: 'Error',
  system: 'http://hl7.org/fhir/endpoint-status',
};
const endpoint_status_Off: Coding = {
  code: 'off',
  display: 'Off',
  system: 'http://hl7.org/fhir/endpoint-status',
};
const endpoint_status_Suspended: Coding = {
  code: 'suspended',
  display: 'Suspended',
  system: 'http://hl7.org/fhir/endpoint-status',
};
const endpoint_status_Test: Coding = {
  code: 'test',
  display: 'Test',
  system: 'http://hl7.org/fhir/endpoint-status',
};
/**
 * The status of the endpoint.
 */
export const EndpointStatus = {
  /**
   * This endpoint is expected to be active and can be used.
   */
  Active: endpoint_status_Active,
  /**
   * This instance should not have been part of this patient's medical record.
   */
  EnteredInError: endpoint_status_EnteredInError,
  /**
   * This endpoint has exceeded connectivity thresholds and is considered in an error state and should no longer be attempted to connect to until corrective action is taken.
   */
  Error: endpoint_status_Error,
  /**
   * This endpoint is no longer to be used.
   */
  Off: endpoint_status_Off,
  /**
   * This endpoint is temporarily unavailable.
   */
  Suspended: endpoint_status_Suspended,
  /**
   * This endpoint is not intended for production usage.
   */
  Test: endpoint_status_Test,
};
const v3_RouteOfAdministration_InstillationEnteralFeedingTube: Coding = {
  code: 'EFT',
  display: 'Instillation, enteral feeding tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationEnteral: Coding = {
  code: 'ENTINSTL',
  display: 'Instillation, enteral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationGastroJejunostomyTube: Coding = {
  code: 'GJT',
  display: 'Instillation, gastro-jejunostomy tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationGastrostomyTube: Coding = {
  code: 'GT',
  display: 'Instillation, gastrostomy tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationJejunostomyTube: Coding = {
  code: 'JJTINSTL',
  display: 'Instillation, jejunostomy tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationNasogastricTube: Coding = {
  code: 'NGT',
  display: 'Instillation, nasogastric tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationOrogastricTube: Coding = {
  code: 'OGT',
  display: 'Instillation, orogastric tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationOrojejunumTube: Coding = {
  code: 'OJJ',
  display: 'Instillation, orojejunum tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SwallowOral: Coding = {
  code: 'PO',
  display: 'Swallow, oral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
/**
 * EnteralRouteOfAdministration: Codes specifying the route of administration of enteral formula.  This value set is composed of HL7 V3 codes and is provided as a suggestive example.
 */
export const EnteralRoute = {
  /**
   * Instillation, enteral feeding tube
   */
  InstillationEnteralFeedingTube: v3_RouteOfAdministration_InstillationEnteralFeedingTube,
  /**
   * Instillation, enteral
   */
  InstillationEnteral: v3_RouteOfAdministration_InstillationEnteral,
  /**
   * Instillation, gastro-jejunostomy tube
   */
  InstillationGastroJejunostomyTube: v3_RouteOfAdministration_InstillationGastroJejunostomyTube,
  /**
   * Instillation, gastrostomy tube
   */
  InstillationGastrostomyTube: v3_RouteOfAdministration_InstillationGastrostomyTube,
  /**
   * Instillation, jejunostomy tube
   */
  InstillationJejunostomyTube: v3_RouteOfAdministration_InstillationJejunostomyTube,
  /**
   * Instillation, nasogastric tube
   */
  InstillationNasogastricTube: v3_RouteOfAdministration_InstillationNasogastricTube,
  /**
   * Instillation, orogastric tube
   */
  InstillationOrogastricTube: v3_RouteOfAdministration_InstillationOrogastricTube,
  /**
   * Instillation, orojejunum tube
   */
  InstillationOrojejunumTube: v3_RouteOfAdministration_InstillationOrojejunumTube,
  /**
   * Swallow, oral
   */
  SwallowOral: v3_RouteOfAdministration_SwallowOral,
};
const entformula_additive_Carbohydrate: Coding = {
  code: 'carbohydrate',
  display: 'Carbohydrate',
  system: 'http://terminology.hl7.org/CodeSystem/entformula-additive',
};
const entformula_additive_Fiber: Coding = {
  code: 'fiber',
  display: 'Fiber',
  system: 'http://terminology.hl7.org/CodeSystem/entformula-additive',
};
const entformula_additive_Lipid: Coding = {
  code: 'lipid',
  display: 'Lipid',
  system: 'http://terminology.hl7.org/CodeSystem/entformula-additive',
};
const entformula_additive_Protein: Coding = {
  code: 'protein',
  display: 'Protein',
  system: 'http://terminology.hl7.org/CodeSystem/entformula-additive',
};
const entformula_additive_Water: Coding = {
  code: 'water',
  display: 'Water',
  system: 'http://terminology.hl7.org/CodeSystem/entformula-additive',
};
/**
 * EnteralFormulaAdditiveType: Codes for the type of modular component such as protein, carbohydrate or fiber to be provided in addition to or mixed with the base formula. This value set is provided as a suggestive example.
 */
export const EntformulaAdditive = {
  /**
   * Modular carbohydrate enteral formula component
   */
  Carbohydrate: entformula_additive_Carbohydrate,
  /**
   * Modular fiber enteral formula component
   */
  Fiber: entformula_additive_Fiber,
  /**
   * Modular lipid enteral formula component
   */
  Lipid: entformula_additive_Lipid,
  /**
   * Modular protein enteral formula component
   */
  Protein: entformula_additive_Protein,
  /**
   * Added water
   */
  Water: entformula_additive_Water,
};
const EntformulaType_StandardEnteralFormula: Coding = {
  code: '441531000124102',
  display: 'Standard Enteral Formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_StandardEnteralFormulaWithFiber: Coding = {
  code: '441561000124106',
  display: 'Standard enteral formula with fiber',
  system: 'http://snomed.info/sct',
};
const EntformulaType_HighEnergyEnteralFormulaWithFiber: Coding = {
  code: '441571000124104',
  display: 'High energy enteral formula with fiber',
  system: 'http://snomed.info/sct',
};
const EntformulaType_DiabeticEnteralFormulaWithFiber: Coding = {
  code: '441591000124103',
  display: 'Diabetic enteral formula with fiber',
  system: 'http://snomed.info/sct',
};
const EntformulaType_DiabeticHighCalorieHighProteinEnteralFormulaWithFiber: Coding = {
  code: '441601000124106',
  display: 'Diabetic high calorie high protein enteral formula with fiber',
  system: 'http://snomed.info/sct',
};
const EntformulaType_HydrolyzedPeptideBasedHighProteinEnteralFormula: Coding = {
  code: '441671000124100',
  display: 'Hydrolyzed peptide-based high protein enteral formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultFormula: Coding = {
  code: '442651000124102',
  display: 'Adult formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultElementalFormula: Coding = {
  code: '442911000124109',
  display: 'Adult elemental formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultStandardFormula: Coding = {
  code: '442921000124101',
  display: 'Adult standard formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultSoyProteinIsolateFormula: Coding = {
  code: '442931000124103',
  display: 'Adult soy protein isolate formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultRenalSpecialtyFormula: Coding = {
  code: '442941000124108',
  display: 'Adult renal specialty formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultPulmonarySpecialtyFormula: Coding = {
  code: '442951000124105',
  display: 'Adult pulmonary specialty formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultLowCarbohydrateFormula: Coding = {
  code: '442961000124107',
  display: 'Adult low carbohydrate formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultHighEnergyFormula: Coding = {
  code: '442971000124100',
  display: 'Adult high energy formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultHydrolyzedProteinFormula: Coding = {
  code: '442981000124102',
  display: 'Adult hydrolyzed protein formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultHighProteinFormula: Coding = {
  code: '442991000124104',
  display: 'Adult high protein formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultHighProteinHighFiberFormula: Coding = {
  code: '443011000124100',
  display: 'Adult high protein high fiber formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultHepaticSpecialtyFormula: Coding = {
  code: '443021000124108',
  display: 'Adult hepatic specialty formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultCriticalCareFormula: Coding = {
  code: '443031000124106',
  display: 'Adult critical care formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_AdultDiabetesSpecialtyFormula: Coding = {
  code: '443051000124104',
  display: 'Adult diabetes specialty formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_HighProteinFormula: Coding = {
  code: '443111000124101',
  display: 'High protein formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_IncreasedFiberFormula: Coding = {
  code: '443351000124102',
  display: 'Increased fiber formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_PediatricFormula: Coding = {
  code: '443361000124100',
  display: 'Pediatric Formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_PediatricElementalFormula: Coding = {
  code: '443401000124105',
  display: 'Pediatric elemental formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_ElementalFormula: Coding = {
  code: '443411000124108',
  display: 'Elemental Formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_PediatricHydrolyzedProteinFormula: Coding = {
  code: '443421000124100',
  display: 'Pediatric hydrolyzed protein formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_HighEnergyFormula: Coding = {
  code: '443431000124102',
  display: 'High Energy Formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_PediatricStandardFormula: Coding = {
  code: '443451000124109',
  display: 'Pediatric standard formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_StandardFormula: Coding = {
  code: '443461000124106',
  display: 'Standard Formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_PediatricIncreasedFiberFormula: Coding = {
  code: '443471000124104',
  display: 'Pediatric increased fiber formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_RenalFormula: Coding = {
  code: '443481000124101',
  display: 'Renal Formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_PediatricHighEnergyFormula: Coding = {
  code: '443491000124103',
  display: 'Pediatric high energy formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_PediatricHighEnergyFormulaWithIncreasedFiber: Coding = {
  code: '443501000124106',
  display: 'Pediatric high energy formula with increased fiber',
  system: 'http://snomed.info/sct',
};
const EntformulaType_SoyBasedFormula: Coding = {
  code: '443561000124107',
  display: 'Soy based formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_HydrolyzedProteinFormula: Coding = {
  code: '443771000124106',
  display: 'Hydrolyzed protein formula',
  system: 'http://snomed.info/sct',
};
const EntformulaType_PediatricReducedEnergyFormula: Coding = {
  code: '444431000124104',
  display: 'Pediatric reduced energy formula',
  system: 'http://snomed.info/sct',
};
/**
 * EnteralFormulaType : Codes for type of enteral formula to be administered to patient.  This value set is composed of SNOMED CT (US Extension) Concepts from SCTID 470581016 (Enteral+supplement feeds hierarchy (product)) and is provided as a suggestive example.
 */
export const EntformulaType = {
  StandardEnteralFormula: EntformulaType_StandardEnteralFormula,
  StandardEnteralFormulaWithFiber: EntformulaType_StandardEnteralFormulaWithFiber,
  HighEnergyEnteralFormulaWithFiber: EntformulaType_HighEnergyEnteralFormulaWithFiber,
  DiabeticEnteralFormulaWithFiber: EntformulaType_DiabeticEnteralFormulaWithFiber,
  DiabeticHighCalorieHighProteinEnteralFormulaWithFiber: EntformulaType_DiabeticHighCalorieHighProteinEnteralFormulaWithFiber,
  HydrolyzedPeptideBasedHighProteinEnteralFormula: EntformulaType_HydrolyzedPeptideBasedHighProteinEnteralFormula,
  AdultFormula: EntformulaType_AdultFormula,
  AdultElementalFormula: EntformulaType_AdultElementalFormula,
  AdultStandardFormula: EntformulaType_AdultStandardFormula,
  AdultSoyProteinIsolateFormula: EntformulaType_AdultSoyProteinIsolateFormula,
  AdultRenalSpecialtyFormula: EntformulaType_AdultRenalSpecialtyFormula,
  AdultPulmonarySpecialtyFormula: EntformulaType_AdultPulmonarySpecialtyFormula,
  AdultLowCarbohydrateFormula: EntformulaType_AdultLowCarbohydrateFormula,
  AdultHighEnergyFormula: EntformulaType_AdultHighEnergyFormula,
  AdultHydrolyzedProteinFormula: EntformulaType_AdultHydrolyzedProteinFormula,
  AdultHighProteinFormula: EntformulaType_AdultHighProteinFormula,
  AdultHighProteinHighFiberFormula: EntformulaType_AdultHighProteinHighFiberFormula,
  AdultHepaticSpecialtyFormula: EntformulaType_AdultHepaticSpecialtyFormula,
  AdultCriticalCareFormula: EntformulaType_AdultCriticalCareFormula,
  AdultDiabetesSpecialtyFormula: EntformulaType_AdultDiabetesSpecialtyFormula,
  HighProteinFormula: EntformulaType_HighProteinFormula,
  IncreasedFiberFormula: EntformulaType_IncreasedFiberFormula,
  PediatricFormula: EntformulaType_PediatricFormula,
  PediatricElementalFormula: EntformulaType_PediatricElementalFormula,
  ElementalFormula: EntformulaType_ElementalFormula,
  PediatricHydrolyzedProteinFormula: EntformulaType_PediatricHydrolyzedProteinFormula,
  HighEnergyFormula: EntformulaType_HighEnergyFormula,
  PediatricStandardFormula: EntformulaType_PediatricStandardFormula,
  StandardFormula: EntformulaType_StandardFormula,
  PediatricIncreasedFiberFormula: EntformulaType_PediatricIncreasedFiberFormula,
  RenalFormula: EntformulaType_RenalFormula,
  PediatricHighEnergyFormula: EntformulaType_PediatricHighEnergyFormula,
  PediatricHighEnergyFormulaWithIncreasedFiber: EntformulaType_PediatricHighEnergyFormulaWithIncreasedFiber,
  SoyBasedFormula: EntformulaType_SoyBasedFormula,
  HydrolyzedProteinFormula: EntformulaType_HydrolyzedProteinFormula,
  PediatricReducedEnergyFormula: EntformulaType_PediatricReducedEnergyFormula,
};
const episode_of_care_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/episode-of-care-status',
};
const episode_of_care_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/episode-of-care-status',
};
const episode_of_care_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/episode-of-care-status',
};
const episode_of_care_status_Finished: Coding = {
  code: 'finished',
  display: 'Finished',
  system: 'http://hl7.org/fhir/episode-of-care-status',
};
const episode_of_care_status_OnHold: Coding = {
  code: 'onhold',
  display: 'On Hold',
  system: 'http://hl7.org/fhir/episode-of-care-status',
};
const episode_of_care_status_Planned: Coding = {
  code: 'planned',
  display: 'Planned',
  system: 'http://hl7.org/fhir/episode-of-care-status',
};
const episode_of_care_status_Waitlist: Coding = {
  code: 'waitlist',
  display: 'Waitlist',
  system: 'http://hl7.org/fhir/episode-of-care-status',
};
/**
 * The status of the episode of care.
 */
export const EpisodeOfCareStatus = {
  /**
   * This episode of care is current.
   */
  Active: episode_of_care_status_Active,
  /**
   * The episode of care was cancelled, or withdrawn from service, often selected during the planned stage as the patient may have gone elsewhere, or the circumstances have changed and the organization is unable to provide the care. It indicates that services terminated outside the planned/expected workflow.
   */
  Cancelled: episode_of_care_status_Cancelled,
  /**
   * This instance should not have been part of this patient's medical record.
   */
  EnteredInError: episode_of_care_status_EnteredInError,
  /**
   * This episode of care is finished and the organization is not expecting to be providing further care to the patient. Can also be known as "closed", "completed" or other similar terms.
   */
  Finished: episode_of_care_status_Finished,
  /**
   * This episode of care is on hold; the organization has limited responsibility for the patient (such as while on respite).
   */
  OnHold: episode_of_care_status_OnHold,
  /**
   * This episode of care is planned to start at the date specified in the period.start. During this status, an organization may perform assessments to determine if the patient is eligible to receive services, or be organizing to make resources available to provide care services.
   */
  Planned: episode_of_care_status_Planned,
  /**
   * This episode has been placed on a waitlist, pending the episode being made active (or cancelled).
   */
  Waitlist: episode_of_care_status_Waitlist,
};
const episodeofcare_type_CommunityBasedAgedCare: Coding = {
  code: 'cacp',
  display: 'Community-based aged care',
  system: 'http://terminology.hl7.org/CodeSystem/episodeofcare-type',
};
const episodeofcare_type_DrugAndAlcoholRehabilitation: Coding = {
  code: 'da',
  display: 'Drug and alcohol rehabilitation',
  system: 'http://terminology.hl7.org/CodeSystem/episodeofcare-type',
};
const episodeofcare_type_PostCoordinatedDiabetesProgram: Coding = {
  code: 'diab',
  display: 'Post coordinated diabetes program',
  system: 'http://terminology.hl7.org/CodeSystem/episodeofcare-type',
};
const episodeofcare_type_HomeAndCommunityCare: Coding = {
  code: 'hacc',
  display: 'Home and Community Care',
  system: 'http://terminology.hl7.org/CodeSystem/episodeofcare-type',
};
const episodeofcare_type_PostAcuteCare: Coding = {
  code: 'pac',
  display: 'Post Acute Care',
  system: 'http://terminology.hl7.org/CodeSystem/episodeofcare-type',
};
/**
 * This example value set defines a set of codes that can be used to express the usage type of an EpisodeOfCare record.
 */
export const EpisodeofcareType = {
  CommunityBasedAgedCare: episodeofcare_type_CommunityBasedAgedCare,
  DrugAndAlcoholRehabilitation: episodeofcare_type_DrugAndAlcoholRehabilitation,
  PostCoordinatedDiabetesProgram: episodeofcare_type_PostCoordinatedDiabetesProgram,
  HomeAndCommunityCare: episodeofcare_type_HomeAndCommunityCare,
  PostAcuteCare: episodeofcare_type_PostAcuteCare,
};
const event_capability_mode_Receiver: Coding = {
  code: 'receiver',
  display: 'Receiver',
  system: 'http://hl7.org/fhir/event-capability-mode',
};
const event_capability_mode_Sender: Coding = {
  code: 'sender',
  display: 'Sender',
  system: 'http://hl7.org/fhir/event-capability-mode',
};
/**
 * The mode of a message capability statement.
 */
export const EventCapabilityMode = {
  /**
   * The application receives requests and sends responses.
   */
  Receiver: event_capability_mode_Receiver,
  /**
   * The application sends requests and receives responses.
   */
  Sender: event_capability_mode_Sender,
};
const event_resource_types_ChargeItem: Coding = {
  code: 'ChargeItem',
  display: 'ChargeItem',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_ClaimResponse: Coding = {
  code: 'ClaimResponse',
  display: 'ClaimResponse',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_ClinicalImpression: Coding = {
  code: 'ClinicalImpression',
  display: 'ClinicalImpression',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_Communication: Coding = {
  code: 'Communication',
  display: 'Communication',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_Composition: Coding = {
  code: 'Composition',
  display: 'Composition',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_Condition: Coding = {
  code: 'Condition',
  display: 'Condition',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_Consent: Coding = {
  code: 'Consent',
  display: 'Consent',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_Coverage: Coding = {
  code: 'Coverage',
  display: 'Coverage',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_DeviceUseStatement: Coding = {
  code: 'DeviceUseStatement',
  display: 'DeviceUseStatement',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_DiagnosticReport: Coding = {
  code: 'DiagnosticReport',
  display: 'DiagnosticReport',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_DocumentManifest: Coding = {
  code: 'DocumentManifest',
  display: 'DocumentManifest',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_DocumentReference: Coding = {
  code: 'DocumentReference',
  display: 'DocumentReference',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_Encounter: Coding = {
  code: 'Encounter',
  display: 'Encounter',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_EnrollmentResponse: Coding = {
  code: 'EnrollmentResponse',
  display: 'EnrollmentResponse',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_EpisodeOfCare: Coding = {
  code: 'EpisodeOfCare',
  display: 'EpisodeOfCare',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_ExplanationOfBenefit: Coding = {
  code: 'ExplanationOfBenefit',
  display: 'ExplanationOfBenefit',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_FamilyMemberHistory: Coding = {
  code: 'FamilyMemberHistory',
  display: 'FamilyMemberHistory',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_GuidanceResponse: Coding = {
  code: 'GuidanceResponse',
  display: 'GuidanceResponse',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_ImagingStudy: Coding = {
  code: 'ImagingStudy',
  display: 'ImagingStudy',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_Immunization: Coding = {
  code: 'Immunization',
  display: 'Immunization',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_MeasureReport: Coding = {
  code: 'MeasureReport',
  display: 'MeasureReport',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_Media: Coding = {
  code: 'Media',
  display: 'Media',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_MedicationAdministration: Coding = {
  code: 'MedicationAdministration',
  display: 'MedicationAdministration',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_MedicationDispense: Coding = {
  code: 'MedicationDispense',
  display: 'MedicationDispense',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_MedicationStatement: Coding = {
  code: 'MedicationStatement',
  display: 'MedicationStatement',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_Observation: Coding = {
  code: 'Observation',
  display: 'Observation',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_PaymentNotice: Coding = {
  code: 'PaymentNotice',
  display: 'PaymentNotice',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_PaymentReconciliation: Coding = {
  code: 'PaymentReconciliation',
  display: 'PaymentReconciliation',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_Procedure: Coding = {
  code: 'Procedure',
  display: 'Procedure',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_ProcessResponse: Coding = {
  code: 'ProcessResponse',
  display: 'ProcessResponse',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_QuestionnaireResponse: Coding = {
  code: 'QuestionnaireResponse',
  display: 'QuestionnaireResponse',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_RiskAssessment: Coding = {
  code: 'RiskAssessment',
  display: 'RiskAssessment',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_SupplyDelivery: Coding = {
  code: 'SupplyDelivery',
  display: 'SupplyDelivery',
  system: 'http://hl7.org/fhir/event-resource-types',
};
const event_resource_types_Task: Coding = {
  code: 'Task',
  display: 'Task',
  system: 'http://hl7.org/fhir/event-resource-types',
};
/**
 * A list of all the event resource types defined in this version of the FHIR specification.
 */
export const EventResourceTypes = {
  /**
   * Item containing charge code(s) associated with the provision of healthcare provider products.
   */
  ChargeItem: event_resource_types_ChargeItem,
  /**
   * Remittance resource.
   */
  ClaimResponse: event_resource_types_ClaimResponse,
  /**
   * A clinical assessment performed when planning treatments and management strategies for a patient.
   */
  ClinicalImpression: event_resource_types_ClinicalImpression,
  /**
   * A record of information transmitted from a sender to a receiver.
   */
  Communication: event_resource_types_Communication,
  /**
   * A set of resources composed into a single coherent clinical statement with clinical attestation.
   */
  Composition: event_resource_types_Composition,
  /**
   * Detailed information about conditions, problems or diagnoses.
   */
  Condition: event_resource_types_Condition,
  /**
   * A healthcare consumer's policy choices to permits or denies recipients or roles to perform actions for specific purposes and periods of time.
   */
  Consent: event_resource_types_Consent,
  /**
   * Insurance or medical plan or a payment agreement.
   */
  Coverage: event_resource_types_Coverage,
  /**
   * Record of use of a device.
   */
  DeviceUseStatement: event_resource_types_DeviceUseStatement,
  /**
   * A Diagnostic report - a combination of request information, atomic results, images, interpretation, as well as formatted reports.
   */
  DiagnosticReport: event_resource_types_DiagnosticReport,
  /**
   * A list that defines a set of documents.
   */
  DocumentManifest: event_resource_types_DocumentManifest,
  /**
   * A reference to a document.
   */
  DocumentReference: event_resource_types_DocumentReference,
  /**
   * An interaction during which services are provided to the patient.
   */
  Encounter: event_resource_types_Encounter,
  /**
   * EnrollmentResponse resource.
   */
  EnrollmentResponse: event_resource_types_EnrollmentResponse,
  /**
   * An association of a Patient with an Organization and  Healthcare Provider(s) for a period of time that the Organization assumes some level of responsibility.
   */
  EpisodeOfCare: event_resource_types_EpisodeOfCare,
  /**
   * Explanation of Benefit resource.
   */
  ExplanationOfBenefit: event_resource_types_ExplanationOfBenefit,
  /**
   * Information about patient's relatives, relevant for patient.
   */
  FamilyMemberHistory: event_resource_types_FamilyMemberHistory,
  /**
   * The formal response to a guidance request.
   */
  GuidanceResponse: event_resource_types_GuidanceResponse,
  /**
   * A set of images produced in single study (one or more series of references images).
   */
  ImagingStudy: event_resource_types_ImagingStudy,
  /**
   * Immunization event information.
   */
  Immunization: event_resource_types_Immunization,
  /**
   * Results of a measure evaluation.
   */
  MeasureReport: event_resource_types_MeasureReport,
  /**
   * A photo, video, or audio recording acquired or used in healthcare. The actual content may be inline or provided by direct reference.
   */
  Media: event_resource_types_Media,
  /**
   * Administration of medication to a patient.
   */
  MedicationAdministration: event_resource_types_MedicationAdministration,
  /**
   * Dispensing a medication to a named patient.
   */
  MedicationDispense: event_resource_types_MedicationDispense,
  /**
   * Record of medication being taken by a patient.
   */
  MedicationStatement: event_resource_types_MedicationStatement,
  /**
   * Measurements and simple assertions.
   */
  Observation: event_resource_types_Observation,
  /**
   * PaymentNotice request.
   */
  PaymentNotice: event_resource_types_PaymentNotice,
  /**
   * PaymentReconciliation resource.
   */
  PaymentReconciliation: event_resource_types_PaymentReconciliation,
  /**
   * An action that is being or was performed on a patient.
   */
  Procedure: event_resource_types_Procedure,
  /**
   * ProcessResponse resource.
   */
  ProcessResponse: event_resource_types_ProcessResponse,
  /**
   * A structured set of questions and their answers.
   */
  QuestionnaireResponse: event_resource_types_QuestionnaireResponse,
  /**
   * Potential outcomes for a subject with likelihood.
   */
  RiskAssessment: event_resource_types_RiskAssessment,
  /**
   * Delivery of bulk Supplies.
   */
  SupplyDelivery: event_resource_types_SupplyDelivery,
  /**
   * A task to be performed.
   */
  Task: event_resource_types_Task,
};
const event_status_NotDone: Coding = {
  code: 'not-done',
  display: 'Not Done',
  system: 'http://hl7.org/fhir/event-status',
};
const event_status_OnHold: Coding = {
  code: 'on-hold',
  display: 'On Hold',
  system: 'http://hl7.org/fhir/event-status',
};
const event_status_Preparation: Coding = {
  code: 'preparation',
  display: 'Preparation',
  system: 'http://hl7.org/fhir/event-status',
};
const event_status_Stopped: Coding = {
  code: 'stopped',
  display: 'Stopped',
  system: 'http://hl7.org/fhir/event-status',
};
const event_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/event-status',
};
/**
 * Codes identifying the lifecycle stage of an event.
 */
export const EventStatus = {
  /**
   * The event has now concluded.
   */
  Completed: event_status_Completed,
  /**
   * This electronic record should never have existed, though it is possible that real-world decisions were based on it.  (If real-world activity has occurred, the status should be "stopped" rather than "entered-in-error".).
   */
  EnteredInError: event_status_EnteredInError,
  /**
   * The event is currently occurring.
   */
  InProgress: event_status_InProgress,
  /**
   * The event was terminated prior to any activity beyond preparation.  I.e. The 'main' activity has not yet begun.  The boundary between preparatory and the 'main' activity is context-specific.
   */
  NotDone: event_status_NotDone,
  /**
   * The event has been temporarily stopped but is expected to resume in the future.
   */
  OnHold: event_status_OnHold,
  /**
   * The core event has not started yet, but some staging activities have begun (e.g. surgical suite preparation).  Preparation stages may be tracked for billing purposes.
   */
  Preparation: event_status_Preparation,
  /**
   * The event was terminated prior to the full completion of the intended activity but after at least some of the 'main' activity (beyond preparation) has occurred.
   */
  Stopped: event_status_Stopped,
  /**
   * The authoring/source system does not know which of the status values currently applies for this event.  Note: This concept is not to be used for "other" - one of the listed statuses is presumed to apply,  but the authoring/source system does not know which.
   */
  Unknown: event_status_Unknown,
};
const v3_TimingEvent_AC: Coding = {
  code: 'AC',
  display: 'AC',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const v3_TimingEvent_ACD: Coding = {
  code: 'ACD',
  display: 'ACD',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const v3_TimingEvent_ACM: Coding = {
  code: 'ACM',
  display: 'ACM',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const v3_TimingEvent_ACV: Coding = {
  code: 'ACV',
  display: 'ACV',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const event_timing_Afternoon: Coding = {
  code: 'AFT',
  display: 'Afternoon',
  system: 'http://hl7.org/fhir/event-timing',
};
const event_timing_EarlyAfternoon: Coding = {
  code: 'AFT.early',
  display: 'Early Afternoon',
  system: 'http://hl7.org/fhir/event-timing',
};
const event_timing_LateAfternoon: Coding = {
  code: 'AFT.late',
  display: 'Late Afternoon',
  system: 'http://hl7.org/fhir/event-timing',
};
const v3_TimingEvent_C: Coding = {
  code: 'C',
  display: 'C',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const v3_TimingEvent_CD: Coding = {
  code: 'CD',
  display: 'CD',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const v3_TimingEvent_CM: Coding = {
  code: 'CM',
  display: 'CM',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const v3_TimingEvent_CV: Coding = {
  code: 'CV',
  display: 'CV',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const event_timing_Evening: Coding = {
  code: 'EVE',
  display: 'Evening',
  system: 'http://hl7.org/fhir/event-timing',
};
const event_timing_EarlyEvening: Coding = {
  code: 'EVE.early',
  display: 'Early Evening',
  system: 'http://hl7.org/fhir/event-timing',
};
const event_timing_LateEvening: Coding = {
  code: 'EVE.late',
  display: 'Late Evening',
  system: 'http://hl7.org/fhir/event-timing',
};
const v3_TimingEvent_HS: Coding = {
  code: 'HS',
  display: 'HS',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const event_timing_Morning: Coding = {
  code: 'MORN',
  display: 'Morning',
  system: 'http://hl7.org/fhir/event-timing',
};
const event_timing_EarlyMorning: Coding = {
  code: 'MORN.early',
  display: 'Early Morning',
  system: 'http://hl7.org/fhir/event-timing',
};
const event_timing_LateMorning: Coding = {
  code: 'MORN.late',
  display: 'Late Morning',
  system: 'http://hl7.org/fhir/event-timing',
};
const event_timing_Night: Coding = {
  code: 'NIGHT',
  display: 'Night',
  system: 'http://hl7.org/fhir/event-timing',
};
const event_timing_Noon: Coding = {
  code: 'NOON',
  display: 'Noon',
  system: 'http://hl7.org/fhir/event-timing',
};
const v3_TimingEvent_PC: Coding = {
  code: 'PC',
  display: 'PC',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const v3_TimingEvent_PCD: Coding = {
  code: 'PCD',
  display: 'PCD',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const v3_TimingEvent_PCM: Coding = {
  code: 'PCM',
  display: 'PCM',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const v3_TimingEvent_PCV: Coding = {
  code: 'PCV',
  display: 'PCV',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const event_timing_AfterSleep: Coding = {
  code: 'PHS',
  display: 'After Sleep',
  system: 'http://hl7.org/fhir/event-timing',
};
const v3_TimingEvent_WAKE: Coding = {
  code: 'WAKE',
  display: 'WAKE',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
/**
 * Real world event relating to the schedule.
 */
export const EventTiming = {
  /**
   * before meal (from lat. ante cibus)
   */
  AC_v3_TimingEvent: v3_TimingEvent_AC,
  /**
   * before lunch (from lat. ante cibus diurnus)
   */
  ACD_v3_TimingEvent: v3_TimingEvent_ACD,
  /**
   * before breakfast (from lat. ante cibus matutinus)
   */
  ACM_v3_TimingEvent: v3_TimingEvent_ACM,
  /**
   * before dinner (from lat. ante cibus vespertinus)
   */
  ACV_v3_TimingEvent: v3_TimingEvent_ACV,
  /**
   * Event occurs during the afternoon. The exact time is unspecified and established by institution convention or patient interpretation.
   */
  Afternoon_event_timing: event_timing_Afternoon,
  /**
   * Event occurs during the early afternoon. The exact time is unspecified and established by institution convention or patient interpretation.
   */
  EarlyAfternoon_event_timing: event_timing_EarlyAfternoon,
  /**
   * Event occurs during the late afternoon. The exact time is unspecified and established by institution convention or patient interpretation.
   */
  LateAfternoon_event_timing: event_timing_LateAfternoon,
  /**
   * Description: meal (from lat. ante cibus)
   */
  C_v3_TimingEvent: v3_TimingEvent_C,
  /**
   * Description: lunch (from lat. cibus diurnus)
   */
  CD_v3_TimingEvent: v3_TimingEvent_CD,
  /**
   * Description: breakfast (from lat. cibus matutinus)
   */
  CM_v3_TimingEvent: v3_TimingEvent_CM,
  /**
   * Description: dinner (from lat. cibus vespertinus)
   */
  CV_v3_TimingEvent: v3_TimingEvent_CV,
  /**
   * Event occurs during the evening. The exact time is unspecified and established by institution convention or patient interpretation.
   */
  Evening_event_timing: event_timing_Evening,
  /**
   * Event occurs during the early evening. The exact time is unspecified and established by institution convention or patient interpretation.
   */
  EarlyEvening_event_timing: event_timing_EarlyEvening,
  /**
   * Event occurs during the late evening. The exact time is unspecified and established by institution convention or patient interpretation.
   */
  LateEvening_event_timing: event_timing_LateEvening,
  /**
   * Description: Prior to beginning a regular period of extended sleep (this would exclude naps).  Note that this might occur at different times of day depending on a person's regular sleep schedule.
   */
  HS_v3_TimingEvent: v3_TimingEvent_HS,
  /**
   * Event occurs during the morning. The exact time is unspecified and established by institution convention or patient interpretation.
   */
  Morning_event_timing: event_timing_Morning,
  /**
   * Event occurs during the early morning. The exact time is unspecified and established by institution convention or patient interpretation.
   */
  EarlyMorning_event_timing: event_timing_EarlyMorning,
  /**
   * Event occurs during the late morning. The exact time is unspecified and established by institution convention or patient interpretation.
   */
  LateMorning_event_timing: event_timing_LateMorning,
  /**
   * Event occurs during the night. The exact time is unspecified and established by institution convention or patient interpretation.
   */
  Night_event_timing: event_timing_Night,
  /**
   * Event occurs around 12:00pm. The exact time is unspecified and established by institution convention or patient interpretation.
   */
  Noon_event_timing: event_timing_Noon,
  /**
   * after meal (from lat. post cibus)
   */
  PC_v3_TimingEvent: v3_TimingEvent_PC,
  /**
   * after lunch (from lat. post cibus diurnus)
   */
  PCD_v3_TimingEvent: v3_TimingEvent_PCD,
  /**
   * after breakfast (from lat. post cibus matutinus)
   */
  PCM_v3_TimingEvent: v3_TimingEvent_PCM,
  /**
   * after dinner (from lat. post cibus vespertinus)
   */
  PCV_v3_TimingEvent: v3_TimingEvent_PCV,
  /**
   * Event occurs [offset] after subject goes to sleep. The exact time is unspecified and established by institution convention or patient interpretation.
   */
  AfterSleep_event_timing: event_timing_AfterSleep,
  /**
   * Description: Upon waking up from a regular period of sleep, in order to start regular activities (this would exclude waking up from a nap or temporarily waking up during a period of sleep)
   *
   *
   *                            Usage Notes: e.g.
   *
   *                         Take pulse rate on waking in management of thyrotoxicosis.
   *
   *                         Take BP on waking in management of hypertension
   *
   *                         Take basal body temperature on waking in establishing date of ovulation
   */
  WAKE_v3_TimingEvent: v3_TimingEvent_WAKE,
};
const evidence_quality_HighQuality: Coding = {
  code: 'high',
  display: 'High quality',
  system: 'http://terminology.hl7.org/CodeSystem/evidence-quality',
};
const evidence_quality_LowQuality: Coding = {
  code: 'low',
  display: 'Low quality',
  system: 'http://terminology.hl7.org/CodeSystem/evidence-quality',
};
const evidence_quality_ModerateQuality: Coding = {
  code: 'moderate',
  display: 'Moderate quality',
  system: 'http://terminology.hl7.org/CodeSystem/evidence-quality',
};
const evidence_quality_VeryLowQuality: Coding = {
  code: 'very-low',
  display: 'Very low quality',
  system: 'http://terminology.hl7.org/CodeSystem/evidence-quality',
};
/**
 * A rating system that describes the quality of evidence such as the GRADE, DynaMed, or Oxford CEBM systems.
 */
export const EvidenceQuality = {
  /**
   * High quality evidence.
   */
  HighQuality: evidence_quality_HighQuality,
  /**
   * Low quality evidence.
   */
  LowQuality: evidence_quality_LowQuality,
  /**
   * Moderate quality evidence.
   */
  ModerateQuality: evidence_quality_ModerateQuality,
  /**
   * Very low quality evidence.
   */
  VeryLowQuality: evidence_quality_VeryLowQuality,
};
const evidence_variant_state_HighRisk: Coding = {
  code: 'high-risk',
  display: 'high risk',
  system: 'http://terminology.hl7.org/CodeSystem/evidence-variant-state',
};
const evidence_variant_state_LowRisk: Coding = {
  code: 'low-risk',
  display: 'low risk',
  system: 'http://terminology.hl7.org/CodeSystem/evidence-variant-state',
};
const evidence_variant_state_MediumRisk: Coding = {
  code: 'medium-risk',
  display: 'medium risk',
  system: 'http://terminology.hl7.org/CodeSystem/evidence-variant-state',
};
/**
 * Used for results by exposure in variant states such as low-risk, medium-risk and high-risk states.
 */
export const EvidenceVariantState = {
  /**
   * high risk estimate.
   */
  HighRisk: evidence_variant_state_HighRisk,
  /**
   * low risk estimate.
   */
  LowRisk: evidence_variant_state_LowRisk,
  /**
   * medium risk estimate.
   */
  MediumRisk: evidence_variant_state_MediumRisk,
};
const ExampleExpansion_CholesterolMolesVolumeInSerumOrPlasma: Coding = {
  code: '14647-2',
  display: 'Cholesterol [Moles/volume] in Serum or Plasma',
  system: 'http://loinc.org',
};
const ExampleExpansion_CholesterolMassVolumeInSerumOrPlasma: Coding = {
  code: '2093-3',
  display: 'Cholesterol [Mass/volume] in Serum or Plasma',
  system: 'http://loinc.org',
};
const ExampleExpansion_CholesterolTriglycerideMassRatioInSerumOrPlasma: Coding = {
  code: '2096-6',
  display: 'Cholesterol/Triglyceride [Mass Ratio] in Serum or Plasma',
  system: 'http://loinc.org',
};
const ExampleExpansion_CholesterolTriglycerideMassRatioInSerumOrPlasma_2: Coding = {
  code: '35200-5',
  display: 'Cholesterol/Triglyceride [Mass Ratio] in Serum or Plasma',
  system: 'http://loinc.org',
};
const ExampleExpansion_CholesterolApolipoproteinBMolarRatioInSerumOrPlasma: Coding = {
  code: '48089-7',
  display: 'Cholesterol/Apolipoprotein B [Molar ratio] in Serum or Plasma',
  system: 'http://loinc.org',
};
const ExampleExpansion_CholesterolMassVolumeInSerumOrPlasmaUltracentrifugate: Coding = {
  code: '48620-9',
  display: 'Cholesterol [Mass/volume] in Serum or Plasma ultracentrifugate',
  system: 'http://loinc.org',
};
const ExampleExpansion_CholesterolPhospholipidMolarRatioInSerumOrPlasma: Coding = {
  code: '55838-7',
  display: 'Cholesterol/Phospholipid [Molar ratio] in Serum or Plasma',
  system: 'http://loinc.org',
};
const ExampleExpansion_CholesterolPercentile: Coding = {
  code: '9342-7',
  display: 'Cholesterol [Percentile]',
  system: 'http://loinc.org',
};
/**
 * This is an example value set that includes all the LOINC codes for serum/plasma cholesterol from v2.36.
 */
export const ExampleExpansion = {
  CholesterolMolesVolumeInSerumOrPlasma: ExampleExpansion_CholesterolMolesVolumeInSerumOrPlasma,
  CholesterolMassVolumeInSerumOrPlasma: ExampleExpansion_CholesterolMassVolumeInSerumOrPlasma,
  CholesterolTriglycerideMassRatioInSerumOrPlasma: ExampleExpansion_CholesterolTriglycerideMassRatioInSerumOrPlasma,
  CholesterolTriglycerideMassRatioInSerumOrPlasma_2: ExampleExpansion_CholesterolTriglycerideMassRatioInSerumOrPlasma_2,
  CholesterolApolipoproteinBMolarRatioInSerumOrPlasma: ExampleExpansion_CholesterolApolipoproteinBMolarRatioInSerumOrPlasma,
  CholesterolMassVolumeInSerumOrPlasmaUltracentrifugate: ExampleExpansion_CholesterolMassVolumeInSerumOrPlasmaUltracentrifugate,
  CholesterolPhospholipidMolarRatioInSerumOrPlasma: ExampleExpansion_CholesterolPhospholipidMolarRatioInSerumOrPlasma,
  CholesterolPercentile: ExampleExpansion_CholesterolPercentile,
};
/**
 * This is an example value set that includes all the LOINC codes for serum/plasma cholesterol from v2.36.
 */
export const ExampleExtensional = {
  CholesterolMolesVolumeInSerumOrPlasma: ExampleExpansion_CholesterolMolesVolumeInSerumOrPlasma,
  CholesterolMassVolumeInSerumOrPlasma: ExampleExpansion_CholesterolMassVolumeInSerumOrPlasma,
  CholesterolTriglycerideMassRatioInSerumOrPlasma: ExampleExpansion_CholesterolTriglycerideMassRatioInSerumOrPlasma_2,
  CholesterolPercentile: ExampleExpansion_CholesterolPercentile,
};
const ExampleHierarchical_Conflict: Coding = {
  code: 'conflict',
  system: '#hacked',
};
const hacked_EditVersionConflict: Coding = {
  code: 'conflict',
  display: 'Edit Version Conflict',
  system: 'http://hl7.org/fhir/hacked',
};
const ExampleHierarchical_Duplicate: Coding = {
  code: 'duplicate',
  system: '#hacked',
};
const hacked_Duplicate: Coding = {
  code: 'duplicate',
  display: 'Duplicate',
  system: 'http://hl7.org/fhir/hacked',
};
const ExampleHierarchical_Exception: Coding = {
  code: 'exception',
  system: '#hacked',
};
const hacked_Exception: Coding = {
  code: 'exception',
  display: 'Exception',
  system: 'http://hl7.org/fhir/hacked',
};
const ExampleHierarchical_Invalid: Coding = {
  code: 'invalid',
  system: '#hacked',
};
const hacked_InvalidContent: Coding = {
  code: 'invalid',
  display: 'Invalid Content',
  system: 'http://hl7.org/fhir/hacked',
};
const ExampleHierarchical_Lock: Coding = {
  code: 'lock',
  system: '#hacked',
};
const hacked_LockError: Coding = {
  code: 'lock-error',
  display: 'Lock Error',
  system: 'http://hl7.org/fhir/hacked',
};
const ExampleHierarchical_Login: Coding = {
  code: 'login',
  system: '#hacked',
};
const hacked_LoginRequired: Coding = {
  code: 'login',
  display: 'Login Required',
  system: 'http://hl7.org/fhir/hacked',
};
const ExampleHierarchical_NotFound: Coding = {
  code: 'not-found',
  system: '#hacked',
};
const hacked_NotFound: Coding = {
  code: 'not-found',
  display: 'Not Found',
  system: 'http://hl7.org/fhir/hacked',
};
const ExampleHierarchical_Processing: Coding = {
  code: 'processing',
  system: '#hacked',
};
const hacked_ProcessingFailure: Coding = {
  code: 'processing',
  display: 'Processing Failure',
  system: 'http://hl7.org/fhir/hacked',
};
const ExampleHierarchical_Required: Coding = {
  code: 'required',
  system: '#hacked',
};
const hacked_RequiredElementMissing: Coding = {
  code: 'required',
  display: 'Required element missing',
  system: 'http://hl7.org/fhir/hacked',
};
const hacked_SecurityProblem: Coding = {
  code: 'security',
  display: 'Security Problem',
  system: 'http://hl7.org/fhir/hacked',
};
const ExampleHierarchical_Structure: Coding = {
  code: 'structure',
  system: '#hacked',
};
const hacked_StructuralIssue: Coding = {
  code: 'structure',
  display: 'Structural Issue',
  system: 'http://hl7.org/fhir/hacked',
};
const ExampleHierarchical_Tbrottled: Coding = {
  code: 'tbrottled',
  system: '#hacked',
};
const hacked_Throttled: Coding = {
  code: 'throttled',
  display: 'Throttled',
  system: 'http://hl7.org/fhir/hacked',
};
const hacked_TransientIssue: Coding = {
  code: 'transient',
  display: 'Transient Issue',
  system: 'http://hl7.org/fhir/hacked',
};
const ExampleHierarchical_Unknown: Coding = {
  code: 'unknown',
  system: '#hacked',
};
const hacked_UnknownUser: Coding = {
  code: 'unknown',
  display: 'Unknown User',
  system: 'http://hl7.org/fhir/hacked',
};
const ExampleHierarchical_Value: Coding = {
  code: 'value',
  system: '#hacked',
};
const hacked_ElementValueInvalid: Coding = {
  code: 'value',
  display: 'Element value invalid',
  system: 'http://hl7.org/fhir/hacked',
};
/**
 * Demonstration of extensions that build a hierarchical contains
 */
export const ExampleHierarchical = {
  Conflict: ExampleHierarchical_Conflict,
  EditVersionConflict: hacked_EditVersionConflict,
  Duplicate: ExampleHierarchical_Duplicate,
  Duplicate_2: hacked_Duplicate,
  Exception: ExampleHierarchical_Exception,
  Exception_2: hacked_Exception,
  Invalid: ExampleHierarchical_Invalid,
  InvalidContent: hacked_InvalidContent,
  Lock: ExampleHierarchical_Lock,
  LockError: hacked_LockError,
  Login: ExampleHierarchical_Login,
  LoginRequired: hacked_LoginRequired,
  NotFound: ExampleHierarchical_NotFound,
  NotFound_2: hacked_NotFound,
  Processing: ExampleHierarchical_Processing,
  ProcessingFailure: hacked_ProcessingFailure,
  Required: ExampleHierarchical_Required,
  RequiredElementMissing: hacked_RequiredElementMissing,
  SecurityProblem: hacked_SecurityProblem,
  Structure: ExampleHierarchical_Structure,
  StructuralIssue: hacked_StructuralIssue,
  Tbrottled: ExampleHierarchical_Tbrottled,
  Throttled: hacked_Throttled,
  TransientIssue: hacked_TransientIssue,
  Unknown: ExampleHierarchical_Unknown,
  UnknownUser: hacked_UnknownUser,
  Value: ExampleHierarchical_Value,
  ElementValueInvalid: hacked_ElementValueInvalid,
};
const examplescenario_actor_type_System: Coding = {
  code: 'entity',
  display: 'System',
  system: 'http://hl7.org/fhir/examplescenario-actor-type',
};
const examplescenario_actor_type_Person: Coding = {
  code: 'person',
  display: 'Person',
  system: 'http://hl7.org/fhir/examplescenario-actor-type',
};
/**
 * The type of actor - system or human.
 */
export const ExamplescenarioActorType = {
  /**
   * A system.
   */
  System: examplescenario_actor_type_System,
  /**
   * A person.
   */
  Person: examplescenario_actor_type_Person,
};
const ex_benefitcategory_MedicalCare: Coding = {
  code: '1',
  display: 'Medical Care',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_RenalSupplies: Coding = {
  code: '14',
  display: 'Renal Supplies',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_Surgical: Coding = {
  code: '2',
  display: 'Surgical',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_DiagnosticDental: Coding = {
  code: '23',
  display: 'Diagnostic Dental',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_Periodontics: Coding = {
  code: '24',
  display: 'Periodontics',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_Restorative: Coding = {
  code: '25',
  display: 'Restorative',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_Endodontics: Coding = {
  code: '26',
  display: 'Endodontics',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_MaxillofacialProsthetics: Coding = {
  code: '27',
  display: 'Maxillofacial Prosthetics',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_AdjunctiveDentalServices: Coding = {
  code: '28',
  display: 'Adjunctive Dental Services',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_Consultation: Coding = {
  code: '3',
  display: 'Consultation',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_HealthBenefitPlanCoverage: Coding = {
  code: '30',
  display: 'Health Benefit Plan Coverage',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_DentalCare: Coding = {
  code: '35',
  display: 'Dental Care',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_DentalCrowns: Coding = {
  code: '36',
  display: 'Dental Crowns',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_DentalAccident: Coding = {
  code: '37',
  display: 'Dental Accident',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_DiagnosticXRay: Coding = {
  code: '4',
  display: 'Diagnostic XRay',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_HospitalRoomAndBoard: Coding = {
  code: '49',
  display: 'Hospital Room and Board',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_DiagnosticLab: Coding = {
  code: '5',
  display: 'Diagnostic Lab',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_MajorMedical: Coding = {
  code: '55',
  display: 'Major Medical',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_MedicallyRelatedTransportation: Coding = {
  code: '56',
  display: 'Medically Related Transportation',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_InVitroFertilization: Coding = {
  code: '61',
  display: 'In-vitro Fertilization',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_MRIScan: Coding = {
  code: '62',
  display: 'MRI Scan',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_DonorProcedures: Coding = {
  code: '63',
  display: 'Donor Procedures',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_Maternity: Coding = {
  code: '69',
  display: 'Maternity',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_RenalDialysis: Coding = {
  code: '76',
  display: 'Renal Dialysis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_MedicalCoverage: Coding = {
  code: 'F1',
  display: 'Medical Coverage',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_DentalCoverage: Coding = {
  code: 'F3',
  display: 'Dental Coverage',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_HearingCoverage: Coding = {
  code: 'F4',
  display: 'Hearing Coverage',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
const ex_benefitcategory_VisionCoverage: Coding = {
  code: 'F6',
  display: 'Vision Coverage',
  system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
};
/**
 * This value set includes examples of Benefit Category codes.
 */
export const ExBenefitcategory = {
  /**
   * Medical Care.
   */
  MedicalCare: ex_benefitcategory_MedicalCare,
  /**
   * Renal Supplies excluding Dialysis.
   */
  RenalSupplies: ex_benefitcategory_RenalSupplies,
  /**
   * Surgical.
   */
  Surgical: ex_benefitcategory_Surgical,
  /**
   * Diagnostic Dental.
   */
  DiagnosticDental: ex_benefitcategory_DiagnosticDental,
  /**
   * Periodontics.
   */
  Periodontics: ex_benefitcategory_Periodontics,
  /**
   * Restorative.
   */
  Restorative: ex_benefitcategory_Restorative,
  /**
   * Endodontics.
   */
  Endodontics: ex_benefitcategory_Endodontics,
  /**
   * Maxillofacial Prosthetics.
   */
  MaxillofacialProsthetics: ex_benefitcategory_MaxillofacialProsthetics,
  /**
   * Adjunctive Dental Services.
   */
  AdjunctiveDentalServices: ex_benefitcategory_AdjunctiveDentalServices,
  /**
   * Consultation.
   */
  Consultation: ex_benefitcategory_Consultation,
  /**
   * Health Benefit Plan Coverage.
   */
  HealthBenefitPlanCoverage: ex_benefitcategory_HealthBenefitPlanCoverage,
  /**
   * Dental Care.
   */
  DentalCare: ex_benefitcategory_DentalCare,
  /**
   * Dental Crowns.
   */
  DentalCrowns: ex_benefitcategory_DentalCrowns,
  /**
   * Dental Accident.
   */
  DentalAccident: ex_benefitcategory_DentalAccident,
  /**
   * Diagnostic XRay.
   */
  DiagnosticXRay: ex_benefitcategory_DiagnosticXRay,
  /**
   * Hospital Room and Board.
   */
  HospitalRoomAndBoard: ex_benefitcategory_HospitalRoomAndBoard,
  /**
   * Diagnostic Lab.
   */
  DiagnosticLab: ex_benefitcategory_DiagnosticLab,
  /**
   * Major Medical.
   */
  MajorMedical: ex_benefitcategory_MajorMedical,
  /**
   * Medically Related Transportation.
   */
  MedicallyRelatedTransportation: ex_benefitcategory_MedicallyRelatedTransportation,
  /**
   * In-vitro Fertilization.
   */
  InVitroFertilization: ex_benefitcategory_InVitroFertilization,
  /**
   * MRI Scan.
   */
  MRIScan: ex_benefitcategory_MRIScan,
  /**
   * Donor Procedures such as organ harvest.
   */
  DonorProcedures: ex_benefitcategory_DonorProcedures,
  /**
   * Maternity.
   */
  Maternity: ex_benefitcategory_Maternity,
  /**
   * Renal dialysis.
   */
  RenalDialysis: ex_benefitcategory_RenalDialysis,
  /**
   * Medical Coverage.
   */
  MedicalCoverage: ex_benefitcategory_MedicalCoverage,
  /**
   * Dental Coverage.
   */
  DentalCoverage: ex_benefitcategory_DentalCoverage,
  /**
   * Hearing Coverage.
   */
  HearingCoverage: ex_benefitcategory_HearingCoverage,
  /**
   * Vision Coverage.
   */
  VisionCoverage: ex_benefitcategory_VisionCoverage,
};
const ex_diagnosis_on_admission_No: Coding = {
  code: 'n',
  display: 'No',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosis-on-admission',
};
const ex_diagnosis_on_admission_Unknown: Coding = {
  code: 'u',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosis-on-admission',
};
const ex_diagnosis_on_admission_Undetermined: Coding = {
  code: 'w',
  display: 'Undetermined',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosis-on-admission',
};
const ex_diagnosis_on_admission_Yes: Coding = {
  code: 'y',
  display: 'Yes',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosis-on-admission',
};
/**
 * This value set includes example Diagnosis on Admission codes.
 */
export const ExDiagnosisOnAdmission = {
  /**
   * Diagnosis was not present at time of inpatient admission.
   */
  No: ex_diagnosis_on_admission_No,
  /**
   * Documentation insufficient to determine if condition was present at the time of inpatient admission.
   */
  Unknown: ex_diagnosis_on_admission_Unknown,
  /**
   * Clinically undetermined. Provider unable to clinically determine whether the condition was present at the time of inpatient admission.
   */
  Undetermined: ex_diagnosis_on_admission_Undetermined,
  /**
   * Diagnosis was present at time of inpatient admission.
   */
  Yes: ex_diagnosis_on_admission_Yes,
};
const ex_diagnosisrelatedgroup_NormalVaginalDelivery: Coding = {
  code: '100',
  display: 'Normal Vaginal Delivery',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosisrelatedgroup',
};
const ex_diagnosisrelatedgroup_AppendectomyUncomplicated: Coding = {
  code: '101',
  display: 'Appendectomy - uncomplicated',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosisrelatedgroup',
};
const ex_diagnosisrelatedgroup_ToothAbscess: Coding = {
  code: '300',
  display: 'Tooth abscess',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosisrelatedgroup',
};
const ex_diagnosisrelatedgroup_HeadTraumaConcussion: Coding = {
  code: '400',
  display: 'Head trauma - concussion',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosisrelatedgroup',
};
/**
 * This value set includes example Diagnosis Related Group codes.
 */
export const ExDiagnosisrelatedgroup = {
  /**
   * Normal Vaginal Delivery.
   */
  NormalVaginalDelivery: ex_diagnosisrelatedgroup_NormalVaginalDelivery,
  /**
   * Appendectomy without rupture or other complications.
   */
  AppendectomyUncomplicated: ex_diagnosisrelatedgroup_AppendectomyUncomplicated,
  /**
   * Emergency department treatment of a tooth abscess.
   */
  ToothAbscess: ex_diagnosisrelatedgroup_ToothAbscess,
  /**
   * Head trauma - concussion.
   */
  HeadTraumaConcussion: ex_diagnosisrelatedgroup_HeadTraumaConcussion,
};
const ex_diagnosistype_AdmittingDiagnosis: Coding = {
  code: 'admitting',
  display: 'Admitting Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosistype',
};
const ex_diagnosistype_ClinicalDiagnosis: Coding = {
  code: 'clinical',
  display: 'Clinical Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosistype',
};
const ex_diagnosistype_DifferentialDiagnosis: Coding = {
  code: 'differential',
  display: 'Differential Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosistype',
};
const ex_diagnosistype_DischargeDiagnosis: Coding = {
  code: 'discharge',
  display: 'Discharge Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosistype',
};
const ex_diagnosistype_LaboratoryDiagnosis: Coding = {
  code: 'laboratory',
  display: 'Laboratory Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosistype',
};
const ex_diagnosistype_NursingDiagnosis: Coding = {
  code: 'nursing',
  display: 'Nursing Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosistype',
};
const ex_diagnosistype_PrenatalDiagnosis: Coding = {
  code: 'prenatal',
  display: 'Prenatal Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosistype',
};
const ex_diagnosistype_PrincipalDiagnosis: Coding = {
  code: 'principal',
  display: 'Principal Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosistype',
};
const ex_diagnosistype_RadiologyDiagnosis: Coding = {
  code: 'radiology',
  display: 'Radiology Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosistype',
};
const ex_diagnosistype_RemoteDiagnosis: Coding = {
  code: 'remote',
  display: 'Remote Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosistype',
};
const ex_diagnosistype_RetrospectiveDiagnosis: Coding = {
  code: 'retrospective',
  display: 'Retrospective Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosistype',
};
const ex_diagnosistype_SelfDiagnosis: Coding = {
  code: 'self',
  display: 'Self Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-diagnosistype',
};
/**
 * This value set includes example Diagnosis Type codes.
 */
export const ExDiagnosistype = {
  /**
   * The diagnosis given as the reason why the patient was admitted to the hospital.
   */
  AdmittingDiagnosis: ex_diagnosistype_AdmittingDiagnosis,
  /**
   * A diagnosis made on the basis of medical signs and patient-reported symptoms, rather than diagnostic tests.
   */
  ClinicalDiagnosis: ex_diagnosistype_ClinicalDiagnosis,
  /**
   * One of a set of the possible diagnoses that could be connected to the signs, symptoms, and lab findings.
   */
  DifferentialDiagnosis: ex_diagnosistype_DifferentialDiagnosis,
  /**
   * The diagnosis given when the patient is discharged from the hospital.
   */
  DischargeDiagnosis: ex_diagnosistype_DischargeDiagnosis,
  /**
   * A diagnosis based significantly on laboratory reports or test results, rather than the physical examination of the patient.
   */
  LaboratoryDiagnosis: ex_diagnosistype_LaboratoryDiagnosis,
  /**
   * A diagnosis which identifies people's responses to situations in their lives, such as a readiness to change or a willingness to accept assistance.
   */
  NursingDiagnosis: ex_diagnosistype_NursingDiagnosis,
  /**
   * A diagnosis determined prior to birth.
   */
  PrenatalDiagnosis: ex_diagnosistype_PrenatalDiagnosis,
  /**
   * The single medical diagnosis that is most relevant to the patient's chief complaint or need for treatment.
   */
  PrincipalDiagnosis: ex_diagnosistype_PrincipalDiagnosis,
  /**
   * A diagnosis based primarily on the results from medical imaging studies.
   */
  RadiologyDiagnosis: ex_diagnosistype_RadiologyDiagnosis,
  /**
   * A diagnosis determined using telemedicine techniques.
   */
  RemoteDiagnosis: ex_diagnosistype_RemoteDiagnosis,
  /**
   * The labeling of an illness in a specific historical event using modern knowledge, methods and disease classifications.
   */
  RetrospectiveDiagnosis: ex_diagnosistype_RetrospectiveDiagnosis,
  /**
   * A diagnosis determined by the patient.
   */
  SelfDiagnosis: ex_diagnosistype_SelfDiagnosis,
};
const ex_onsettype_LastMenstruation: Coding = {
  code: 'lmn',
  display: 'Last Menstruation',
  system: 'http://hl7.org/fhir/ex-onsettype',
};
const ex_onsettype_LastExam: Coding = {
  code: 'lxm',
  display: 'Last Exam',
  system: 'http://hl7.org/fhir/ex-onsettype',
};
const ex_onsettype_StartOfSymptoms: Coding = {
  code: 'sym',
  display: 'Start of Symptoms',
  system: 'http://hl7.org/fhir/ex-onsettype',
};
/**
 * This value set includes sample Service Modifier codes.
 */
export const ExOnsettype = {
  /**
   * Start date of last menstruation.
   */
  LastMenstruation: ex_onsettype_LastMenstruation,
  /**
   * Date of last examination.
   */
  LastExam: ex_onsettype_LastExam,
  /**
   * Date when symptoms were first noticed.
   */
  StartOfSymptoms: ex_onsettype_StartOfSymptoms,
};
const expansion_parameter_source_CodeSystem: Coding = {
  code: 'codesystem',
  display: 'Code System',
  system: 'http://terminology.hl7.org/CodeSystem/expansion-parameter-source',
};
const expansion_parameter_source_ClientInput: Coding = {
  code: 'input',
  display: 'Client Input',
  system: 'http://terminology.hl7.org/CodeSystem/expansion-parameter-source',
};
const expansion_parameter_source_ServerEngine: Coding = {
  code: 'server',
  display: 'Server Engine',
  system: 'http://terminology.hl7.org/CodeSystem/expansion-parameter-source',
};
/**
 * Declares what the source of a parameter is.
 */
export const ExpansionParameterSource = {
  /**
   * The parameter was added from one the code systems used in the $expand operation.
   */
  CodeSystem: expansion_parameter_source_CodeSystem,
  /**
   * The parameter was supplied by the client in the $expand request.
   */
  ClientInput: expansion_parameter_source_ClientInput,
  /**
   * The parameter was added by the expansion engine on the server.
   */
  ServerEngine: expansion_parameter_source_ServerEngine,
};
const expansion_processing_rule_AllCodes: Coding = {
  code: 'all-codes',
  display: 'All Codes',
  system: 'http://terminology.hl7.org/CodeSystem/expansion-processing-rule',
};
const expansion_processing_rule_GroupsOnly: Coding = {
  code: 'groups-only',
  display: 'Groups Only',
  system: 'http://terminology.hl7.org/CodeSystem/expansion-processing-rule',
};
const expansion_processing_rule_GroupsPlusUngroupedCodes: Coding = {
  code: 'ungrouped',
  display: 'Groups + Ungrouped codes',
  system: 'http://terminology.hl7.org/CodeSystem/expansion-processing-rule',
};
/**
 * Defines how concepts are processed into the expansion when it's for UI presentation.
 */
export const ExpansionProcessingRule = {
  /**
   * The expansion (when in UI mode) includes all codes *and* any defined groups (in extensions).
   */
  AllCodes: expansion_processing_rule_AllCodes,
  /**
   * The expansion (when in UI mode) only includes the defined groups.
   */
  GroupsOnly: expansion_processing_rule_GroupsOnly,
  /**
   * The expanion (when in UI mode) lists the groups, and then any codes that have not been included in a group.
   */
  GroupsPlusUngroupedCodes: expansion_processing_rule_GroupsPlusUngroupedCodes,
};
const ex_payee_resource_type_Organization: Coding = {
  code: 'organization',
  display: 'Organization',
  system: 'http://terminology.hl7.org/CodeSystem/ex-payee-resource-type',
};
const ex_payee_resource_type_Patient: Coding = {
  code: 'patient',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/ex-payee-resource-type',
};
const ex_payee_resource_type_Practitioner: Coding = {
  code: 'practitioner',
  display: 'Practitioner',
  system: 'http://terminology.hl7.org/CodeSystem/ex-payee-resource-type',
};
const ex_payee_resource_type_RelatedPerson: Coding = {
  code: 'relatedperson',
  display: 'RelatedPerson',
  system: 'http://terminology.hl7.org/CodeSystem/ex-payee-resource-type',
};
/**
 * The type of Claim payee Resource.
 */
export const ExPayeeResourceType = {
  /**
   * Organization resource.
   */
  Organization: ex_payee_resource_type_Organization,
  /**
   * Patient resource.
   */
  Patient: ex_payee_resource_type_Patient,
  /**
   * Practitioner resource.
   */
  Practitioner: ex_payee_resource_type_Practitioner,
  /**
   * RelatedPerson resource.
   */
  RelatedPerson: ex_payee_resource_type_RelatedPerson,
};
const ex_paymenttype_Complete: Coding = {
  code: 'complete',
  display: 'Complete',
  system: 'http://terminology.hl7.org/CodeSystem/ex-paymenttype',
};
const ex_paymenttype_Partial: Coding = {
  code: 'partial',
  display: 'Partial',
  system: 'http://terminology.hl7.org/CodeSystem/ex-paymenttype',
};
/**
 * This value set includes example Payment Type codes.
 */
export const ExPaymenttype = {
  /**
   * Complete (final) payment of the benefit under the Claim less any adjustments.
   */
  Complete: ex_paymenttype_Complete,
  /**
   * Partial payment of the benefit under the Claim less any adjustments.
   */
  Partial: ex_paymenttype_Partial,
};
const explanationofbenefit_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/explanationofbenefit-status',
};
const explanationofbenefit_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/explanationofbenefit-status',
};
const explanationofbenefit_status_Draft: Coding = {
  code: 'draft',
  display: 'Draft',
  system: 'http://hl7.org/fhir/explanationofbenefit-status',
};
const explanationofbenefit_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered In Error',
  system: 'http://hl7.org/fhir/explanationofbenefit-status',
};
/**
 * A code specifying the state of the resource instance.
 */
export const ExplanationofbenefitStatus = {
  /**
   * The resource instance is currently in-force.
   */
  Active: explanationofbenefit_status_Active,
  /**
   * The resource instance is withdrawn, rescinded or reversed.
   */
  Cancelled: explanationofbenefit_status_Cancelled,
  /**
   * A new resource instance the contents of which is not complete.
   */
  Draft: explanationofbenefit_status_Draft,
  /**
   * The resource instance was entered in error.
   */
  EnteredInError: explanationofbenefit_status_EnteredInError,
};
const exposure_state_Exposure: Coding = {
  code: 'exposure',
  display: 'Exposure',
  system: 'http://hl7.org/fhir/exposure-state',
};
const exposure_state_ExposureAlternative: Coding = {
  code: 'exposure-alternative',
  display: 'Exposure Alternative',
  system: 'http://hl7.org/fhir/exposure-state',
};
/**
 * Whether the results by exposure is describing the results for the primary exposure of interest (exposure) or the alternative state (exposureAlternative).
 */
export const ExposureState = {
  /**
   * used when the results by exposure is describing the results for the primary exposure of interest.
   */
  Exposure: exposure_state_Exposure,
  /**
   * used when the results by exposure is describing the results for the alternative exposure state, control state or comparator state.
   */
  ExposureAlternative: exposure_state_ExposureAlternative,
};
const expression_language_FHIRQuery: Coding = {
  code: 'application/x-fhir-query',
  display: 'FHIR Query',
  system: 'http://hl7.org/fhir/expression-language',
};
const expression_language_CQL: Coding = {
  code: 'text/cql',
  display: 'CQL',
  system: 'http://hl7.org/fhir/expression-language',
};
const expression_language_FHIRPath: Coding = {
  code: 'text/fhirpath',
  display: 'FHIRPath',
  system: 'http://hl7.org/fhir/expression-language',
};
/**
 * The media type of the expression language.
 */
export const ExpressionLanguage = {
  /**
   * FHIR's RESTful query syntax - typically independent of base URL.
   */
  FHIRQuery: expression_language_FHIRQuery,
  /**
   * Clinical Quality Language.
   */
  CQL: expression_language_CQL,
  /**
   * FHIRPath.
   */
  FHIRPath: expression_language_FHIRPath,
};
const ex_procedure_type_PrimaryProcedure: Coding = {
  code: 'primary',
  display: 'Primary procedure',
  system: 'http://terminology.hl7.org/CodeSystem/ex-procedure-type',
};
const ex_procedure_type_SecondaryProcedure: Coding = {
  code: 'secondary',
  display: 'Secondary procedure',
  system: 'http://terminology.hl7.org/CodeSystem/ex-procedure-type',
};
/**
 * This value set includes example Procedure Type codes.
 */
export const ExProcedureType = {
  /**
   * The first procedure in a series required to produce and overall patient outcome.
   */
  PrimaryProcedure: ex_procedure_type_PrimaryProcedure,
  /**
   * The second procedure in a series required to produce and overall patient outcome.
   */
  SecondaryProcedure: ex_procedure_type_SecondaryProcedure,
};
const ex_program_code_ChildAsthma: Coding = {
  code: 'as',
  display: 'Child Asthma',
  system: 'http://terminology.hl7.org/CodeSystem/ex-programcode',
};
const ex_program_code_AutismScreening: Coding = {
  code: 'auscr',
  display: 'Autism Screening',
  system: 'http://terminology.hl7.org/CodeSystem/ex-programcode',
};
const ex_program_code_Hemodialysis: Coding = {
  code: 'hd',
  display: 'Hemodialysis',
  system: 'http://terminology.hl7.org/CodeSystem/ex-programcode',
};
const ex_program_code_None: Coding = {
  code: 'none',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/ex-programcode',
};
/**
 * This value set includes sample Program Reason Span codes.
 */
export const ExProgramCode = {
  /**
   * Child Asthma Program
   */
  ChildAsthma: ex_program_code_ChildAsthma,
  /**
   * Autism Screening Program.
   */
  AutismScreening: ex_program_code_AutismScreening,
  /**
   * Hemodialysis Program.
   */
  Hemodialysis: ex_program_code_Hemodialysis,
  /**
   * No program code applies.
   */
  None: ex_program_code_None,
};
const ex_revenue_center_VisionClinic: Coding = {
  code: '0010',
  display: 'Vision Clinic',
  system: 'http://terminology.hl7.org/CodeSystem/ex-revenue-center',
};
const ex_revenue_center_Anaesthesia: Coding = {
  code: '0370',
  display: 'Anaesthesia',
  system: 'http://terminology.hl7.org/CodeSystem/ex-revenue-center',
};
const ex_revenue_center_PhysicalTherapy: Coding = {
  code: '0420',
  display: 'Physical Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/ex-revenue-center',
};
const ex_revenue_center_PhysicalTherapy_2: Coding = {
  code: '0421',
  display: 'Physical Therapy - ',
  system: 'http://terminology.hl7.org/CodeSystem/ex-revenue-center',
};
const ex_revenue_center_SpeechLanguagePathology: Coding = {
  code: '0440',
  display: 'Speech-Language Pathology',
  system: 'http://terminology.hl7.org/CodeSystem/ex-revenue-center',
};
const ex_revenue_center_SpeechLanguagePathologyVisit: Coding = {
  code: '0441',
  display: 'Speech-Language Pathology - Visit',
  system: 'http://terminology.hl7.org/CodeSystem/ex-revenue-center',
};
const ex_revenue_center_EmergencyRoom: Coding = {
  code: '0450',
  display: 'Emergency Room',
  system: 'http://terminology.hl7.org/CodeSystem/ex-revenue-center',
};
const ex_revenue_center_EmergencyRoomEMEMTALA: Coding = {
  code: '0451',
  display: 'Emergency Room - EM/EMTALA',
  system: 'http://terminology.hl7.org/CodeSystem/ex-revenue-center',
};
const ex_revenue_center_EmergencyRoomBeyondEMTALA: Coding = {
  code: '0452',
  display: 'Emergency Room - beyond EMTALA',
  system: 'http://terminology.hl7.org/CodeSystem/ex-revenue-center',
};
/**
 * This value set includes sample Revenue Center codes.
 */
export const ExRevenueCenter = {
  /**
   * Vision Clinic
   */
  VisionClinic: ex_revenue_center_VisionClinic,
  /**
   * Anaesthesia.
   */
  Anaesthesia: ex_revenue_center_Anaesthesia,
  /**
   * Physical Therapy.
   */
  PhysicalTherapy: ex_revenue_center_PhysicalTherapy,
  /**
   * Physical Therapy - visit charge.
   */
  PhysicalTherapy_2: ex_revenue_center_PhysicalTherapy_2,
  /**
   * Speech-Language Pathology.
   */
  SpeechLanguagePathology: ex_revenue_center_SpeechLanguagePathology,
  /**
   * Speech-Language Pathology- visit charge
   */
  SpeechLanguagePathologyVisit: ex_revenue_center_SpeechLanguagePathologyVisit,
  /**
   * Emergency Room
   */
  EmergencyRoom: ex_revenue_center_EmergencyRoom,
  /**
   * Emergency Room - EM/EMTALA
   */
  EmergencyRoomEMEMTALA: ex_revenue_center_EmergencyRoomEMEMTALA,
  /**
   * Emergency Room - beyond EMTALA
   */
  EmergencyRoomBeyondEMTALA: ex_revenue_center_EmergencyRoomBeyondEMTALA,
};
const extension_context_type_ElementID: Coding = {
  code: 'element',
  display: 'Element ID',
  system: 'http://hl7.org/fhir/extension-context-type',
};
const extension_context_type_ExtensionURL: Coding = {
  code: 'extension',
  display: 'Extension URL',
  system: 'http://hl7.org/fhir/extension-context-type',
};
const extension_context_type_FHIRPath: Coding = {
  code: 'fhirpath',
  display: 'FHIRPath',
  system: 'http://hl7.org/fhir/extension-context-type',
};
/**
 * How an extension context is interpreted.
 */
export const ExtensionContextType = {
  /**
   * The context is any element that has an ElementDefinition.id that matches that found in the expression. This includes ElementDefinition Ids that have slicing identifiers. The full path for the element is [url]#[elementid]. If there is no #, the Element id is one defined in the base specification.
   */
  ElementID: extension_context_type_ElementID,
  /**
   * The context is a particular extension from a particular StructureDefinition, and the expression is just a uri that identifies the extension.
   */
  ExtensionURL: extension_context_type_ExtensionURL,
  /**
   * The context is all elements that match the FHIRPath query found in the expression.
   */
  FHIRPath: extension_context_type_FHIRPath,
};
const feeding_device_AngledUtensils: Coding = {
  code: 'angled-utensil',
  display: 'Angled utensils',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_AutomatedFeedingDevices: Coding = {
  code: 'autofeeding-device',
  display: 'Automated feeding devices',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_EnlargedCrossCutNipple: Coding = {
  code: 'bigcut-nipple',
  display: 'Enlarged, cross-cut nipple',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_FoamHandleUtensils: Coding = {
  code: 'foam-handle',
  display: 'Foam handle utensils',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_GlassWithLidSippyCup: Coding = {
  code: 'glass-lid',
  display: 'Glass with lid/sippy cup',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_HabermanBottle: Coding = {
  code: 'haberman-bottle',
  display: 'Haberman bottle',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_DoubleHandholdOnGlassCup: Coding = {
  code: 'handhold-cup',
  display: 'Double handhold on glass/cup',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_MiddleFlowNipple: Coding = {
  code: 'midflo-nipple',
  display: 'Middle flow nipple',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_NoseCup: Coding = {
  code: 'nose-cup',
  display: 'Nose cup',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_OrthodonticNipple: Coding = {
  code: 'ortho-nipple',
  display: 'Orthodontic nipple',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_PreemieNipple: Coding = {
  code: 'preemie-nipple',
  display: 'Preemie nipple',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_ProvaleCup: Coding = {
  code: 'provale-cup',
  display: 'Provale Cup',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_RockerKnife: Coding = {
  code: 'rocker-knife',
  display: 'Rocker knife',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_RubberMattingUnderTray: Coding = {
  code: 'rubber-mat',
  display: 'Rubber matting under tray',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_ScoopPlate: Coding = {
  code: 'scoop-plate',
  display: 'Scoop plate',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_SippyCupWithoutValve: Coding = {
  code: 'sippy-no-valve',
  display: 'Sippy cup without valve',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_SippyCupWithValve: Coding = {
  code: 'sippy-valve',
  display: 'Sippy cup with valve',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_SlowFlowNipple: Coding = {
  code: 'sloflo-nipple',
  display: 'Slow flow nipple',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_SpoutCup: Coding = {
  code: 'spout-cup',
  display: 'Spout cup',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_StandardNipple: Coding = {
  code: 'standard-nipple',
  display: 'Standard nipple',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_Straw: Coding = {
  code: 'straw',
  display: 'Straw',
  system: 'http://hl7.org/fhir/feeding-device',
};
const feeding_device_HandWrapUtensilHolder: Coding = {
  code: 'utensil-holder',
  display: 'Hand wrap utensil holder',
  system: 'http://hl7.org/fhir/feeding-device',
};
/**
 * Materials used or needed to feed the patient.
 */
export const FeedingDevice = {
  /**
   * Angled utensils definition:
   */
  AngledUtensils_feeding_device: feeding_device_AngledUtensils,
  /**
   * Automated feeding devices definition:
   */
  AutomatedFeedingDevices_feeding_device: feeding_device_AutomatedFeedingDevices,
  /**
   * Enlarged, cross-cut nipple definition:
   */
  EnlargedCrossCutNipple_feeding_device: feeding_device_EnlargedCrossCutNipple,
  /**
   * Foam handle utensils definition:
   */
  FoamHandleUtensils_feeding_device: feeding_device_FoamHandleUtensils,
  /**
   * Glass with lid/sippy cup definition:
   */
  GlassWithLidSippyCup_feeding_device: feeding_device_GlassWithLidSippyCup,
  /**
   * Haberman bottle definition:
   */
  HabermanBottle_feeding_device: feeding_device_HabermanBottle,
  /**
   * Double handhold on glass/cup definition:
   */
  DoubleHandholdOnGlassCup_feeding_device: feeding_device_DoubleHandholdOnGlassCup,
  /**
   * Middle flow nipple definition:
   */
  MiddleFlowNipple_feeding_device: feeding_device_MiddleFlowNipple,
  /**
   * Nose cup definition:
   */
  NoseCup_feeding_device: feeding_device_NoseCup,
  /**
   * Orthodontic nipple definition:
   */
  OrthodonticNipple_feeding_device: feeding_device_OrthodonticNipple,
  /**
   * Preemie nipple definition:
   */
  PreemieNipple_feeding_device: feeding_device_PreemieNipple,
  /**
   * Provale Cup definition:
   */
  ProvaleCup_feeding_device: feeding_device_ProvaleCup,
  /**
   * Rocker knife definition:
   */
  RockerKnife_feeding_device: feeding_device_RockerKnife,
  /**
   * Rubber matting under tray definition:
   */
  RubberMattingUnderTray_feeding_device: feeding_device_RubberMattingUnderTray,
  /**
   * Scoop plate definition:
   */
  ScoopPlate_feeding_device: feeding_device_ScoopPlate,
  /**
   * Sippy cup without valve definition:
   */
  SippyCupWithoutValve_feeding_device: feeding_device_SippyCupWithoutValve,
  /**
   * Sippy cup with valve definition:
   */
  SippyCupWithValve_feeding_device: feeding_device_SippyCupWithValve,
  /**
   * Slow flow nipple definition:
   */
  SlowFlowNipple_feeding_device: feeding_device_SlowFlowNipple,
  /**
   * Spout cup definition:
   */
  SpoutCup_feeding_device: feeding_device_SpoutCup,
  /**
   * Standard nipple definition:
   */
  StandardNipple_feeding_device: feeding_device_StandardNipple,
  /**
   * Straw definition:
   */
  Straw_feeding_device: feeding_device_Straw,
  /**
   * Hand wrap utensil holder definition:
   */
  HandWrapUtensilHolder_feeding_device: feeding_device_HandWrapUtensilHolder,
};
const FHIR_version_VAL0080: Coding = {
  code: '0.0.80',
  display: '0.0.80',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL0081: Coding = {
  code: '0.0.81',
  display: '0.0.81',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL0082: Coding = {
  code: '0.0.82',
  display: '0.0.82',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL001: Coding = {
  code: '0.01',
  display: '0.01',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL005: Coding = {
  code: '0.05',
  display: '0.05',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL006: Coding = {
  code: '0.06',
  display: '0.06',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL011: Coding = {
  code: '0.11',
  display: '0.11',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL040: Coding = {
  code: '0.4.0',
  display: '0.4.0',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL050: Coding = {
  code: '0.5.0',
  display: '0.5.0',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL100: Coding = {
  code: '1.0.0',
  display: '1.0.0',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL101: Coding = {
  code: '1.0.1',
  display: '1.0.1',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL102: Coding = {
  code: '1.0.2',
  display: '1.0.2',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL110: Coding = {
  code: '1.1.0',
  display: '1.1.0',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL140: Coding = {
  code: '1.4.0',
  display: '1.4.0',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL160: Coding = {
  code: '1.6.0',
  display: '1.6.0',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL180: Coding = {
  code: '1.8.0',
  display: '1.8.0',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL300: Coding = {
  code: '3.0.0',
  display: '3.0.0',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL301: Coding = {
  code: '3.0.1',
  display: '3.0.1',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL330: Coding = {
  code: '3.3.0',
  display: '3.3.0',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL350: Coding = {
  code: '3.5.0',
  display: '3.5.0',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL400: Coding = {
  code: '4.0.0',
  display: '4.0.0',
  system: 'http://hl7.org/fhir/FHIR-version',
};
const FHIR_version_VAL401: Coding = {
  code: '4.0.1',
  display: '4.0.1',
  system: 'http://hl7.org/fhir/FHIR-version',
};
/**
 * All published FHIR Versions.
 */
export const FHIRVersion = {
  /**
   * DSTU 1 Official version.
   */
  VAL0080: FHIR_version_VAL0080,
  /**
   * DSTU 1 Official version Technical Errata #1.
   */
  VAL0081: FHIR_version_VAL0081,
  /**
   * DSTU 1 Official version Technical Errata #2.
   */
  VAL0082: FHIR_version_VAL0082,
  /**
   * Oldest archived version of FHIR.
   */
  VAL001: FHIR_version_VAL001,
  /**
   * 1st Draft for Comment (Sept 2012 Ballot).
   */
  VAL005: FHIR_version_VAL005,
  /**
   * 2nd Draft for Comment (January 2013 Ballot).
   */
  VAL006: FHIR_version_VAL006,
  /**
   * DSTU 1 Ballot version.
   */
  VAL011: FHIR_version_VAL011,
  /**
   * Draft For Comment (January 2015 Ballot).
   */
  VAL040: FHIR_version_VAL040,
  /**
   * DSTU 2 Ballot version (May 2015 Ballot).
   */
  VAL050: FHIR_version_VAL050,
  /**
   * DSTU 2 QA Preview + CQIF Ballot (Sep 2015).
   */
  VAL100: FHIR_version_VAL100,
  /**
   * DSTU 2 (Official version).
   */
  VAL101: FHIR_version_VAL101,
  /**
   * DSTU 2 (Official version) with 1 technical errata.
   */
  VAL102: FHIR_version_VAL102,
  /**
   * GAO Ballot + draft changes to main FHIR standard.
   */
  VAL110: FHIR_version_VAL110,
  /**
   * CQF on FHIR Ballot + Connectathon 12 (Montreal).
   */
  VAL140: FHIR_version_VAL140,
  /**
   * FHIR STU3 Ballot + Connectathon 13 (Baltimore).
   */
  VAL160: FHIR_version_VAL160,
  /**
   * FHIR STU3 Candidate + Connectathon 14 (San Antonio).
   */
  VAL180: FHIR_version_VAL180,
  /**
   * FHIR Release 3 (STU).
   */
  VAL300: FHIR_version_VAL300,
  /**
   * FHIR Release 3 (STU) with 1 technical errata.
   */
  VAL301: FHIR_version_VAL301,
  /**
   * R4 Ballot #1.
   */
  VAL330: FHIR_version_VAL330,
  /**
   * R4 Ballot #2.
   */
  VAL350: FHIR_version_VAL350,
  /**
   * FHIR Release 4 (Normative + STU).
   */
  VAL400: FHIR_version_VAL400,
  /**
   * FHIR Release 4 Technical Correction.
   */
  VAL401: FHIR_version_VAL401,
};
const filter_operator_Equals: Coding = {
  code: '=',
  display: 'Equals',
  system: 'http://hl7.org/fhir/filter-operator',
};
const filter_operator_DescendentOfBySubsumption: Coding = {
  code: 'descendent-of',
  display: 'Descendent Of (by subsumption)',
  system: 'http://hl7.org/fhir/filter-operator',
};
const filter_operator_Exists: Coding = {
  code: 'exists',
  display: 'Exists',
  system: 'http://hl7.org/fhir/filter-operator',
};
const filter_operator_GeneralizesBySubsumption: Coding = {
  code: 'generalizes',
  display: 'Generalizes (by Subsumption)',
  system: 'http://hl7.org/fhir/filter-operator',
};
const filter_operator_InSet: Coding = {
  code: 'in',
  display: 'In Set',
  system: 'http://hl7.org/fhir/filter-operator',
};
const filter_operator_IsABySubsumption: Coding = {
  code: 'is-a',
  display: 'Is A (by subsumption)',
  system: 'http://hl7.org/fhir/filter-operator',
};
const filter_operator_NotIsABySubsumption: Coding = {
  code: 'is-not-a',
  display: 'Not (Is A) (by subsumption)',
  system: 'http://hl7.org/fhir/filter-operator',
};
const filter_operator_NotInSet: Coding = {
  code: 'not-in',
  display: 'Not in Set',
  system: 'http://hl7.org/fhir/filter-operator',
};
const filter_operator_RegularExpression: Coding = {
  code: 'regex',
  display: 'Regular Expression',
  system: 'http://hl7.org/fhir/filter-operator',
};
/**
 * The kind of operation to perform as a part of a property based filter.
 */
export const FilterOperator = {
  /**
   * The specified property of the code equals the provided value.
   */
  Equals: filter_operator_Equals,
  /**
   * Includes all concept ids that have a transitive is-a relationship with the concept Id provided as the value, excluding the provided concept itself i.e. include descendant codes only).
   */
  DescendentOfBySubsumption: filter_operator_DescendentOfBySubsumption,
  /**
   * The specified property of the code has at least one value (if the specified value is true; if the specified value is false, then matches when the specified property of the code has no values).
   */
  Exists: filter_operator_Exists,
  /**
   * Includes all concept ids that have a transitive is-a relationship from the concept Id provided as the value, including the provided concept itself (i.e. include ancestor codes and self).
   */
  GeneralizesBySubsumption: filter_operator_GeneralizesBySubsumption,
  /**
   * The specified property of the code is in the set of codes or concepts specified in the provided value (comma separated list).
   */
  InSet: filter_operator_InSet,
  /**
   * Includes all concept ids that have a transitive is-a relationship with the concept Id provided as the value, including the provided concept itself (include descendant codes and self).
   */
  IsABySubsumption: filter_operator_IsABySubsumption,
  /**
   * The specified property of the code does not have an is-a relationship with the provided value.
   */
  NotIsABySubsumption: filter_operator_NotIsABySubsumption,
  /**
   * The specified property of the code is not in the set of codes or concepts specified in the provided value (comma separated list).
   */
  NotInSet: filter_operator_NotInSet,
  /**
   * The specified property of the code  matches the regex specified in the provided value.
   */
  RegularExpression: filter_operator_RegularExpression,
};
const financial_taskcode_Cancel: Coding = {
  code: 'cancel',
  display: 'Cancel',
  system: 'http://terminology.hl7.org/CodeSystem/financialtaskcode',
};
const financial_taskcode_Poll: Coding = {
  code: 'poll',
  display: 'Poll',
  system: 'http://terminology.hl7.org/CodeSystem/financialtaskcode',
};
const financial_taskcode_Release: Coding = {
  code: 'release',
  display: 'Release',
  system: 'http://terminology.hl7.org/CodeSystem/financialtaskcode',
};
const financial_taskcode_Reprocess: Coding = {
  code: 'reprocess',
  display: 'Reprocess',
  system: 'http://terminology.hl7.org/CodeSystem/financialtaskcode',
};
const financial_taskcode_StatusCheck: Coding = {
  code: 'status',
  display: 'Status check',
  system: 'http://terminology.hl7.org/CodeSystem/financialtaskcode',
};
/**
 * This value set includes Financial Task codes.
 */
export const FinancialTaskcode = {
  /**
   * Cancel or reverse a resource, such as a claim or preauthorization, which is in-process or complete.
   */
  Cancel: financial_taskcode_Cancel,
  /**
   * Retrieve selected or all queued resources or messages.
   */
  Poll: financial_taskcode_Poll,
  /**
   * Release any reserved funds or material obligations associated with a resource. For example, any unused but reserved funds or treatment allowance associated with a preauthorization once treatment is complete.
   */
  Release: financial_taskcode_Release,
  /**
   * Indication that the processing of a resource, such as a claim, for some or all of the required work is now being requested.
   */
  Reprocess: financial_taskcode_Reprocess,
  /**
   * Check on the processing status of a resource such as the adjudication of a claim.
   */
  StatusCheck: financial_taskcode_StatusCheck,
};
const financial_taskinputtype_Exclude: Coding = {
  code: 'exclude',
  display: 'Exclude',
  system: 'http://terminology.hl7.org/CodeSystem/financialtaskinputtype',
};
const financial_taskinputtype_Include: Coding = {
  code: 'include',
  display: 'Include',
  system: 'http://terminology.hl7.org/CodeSystem/financialtaskinputtype',
};
const financial_taskinputtype_ItemNumber: Coding = {
  code: 'item',
  display: 'Item Number',
  system: 'http://terminology.hl7.org/CodeSystem/financialtaskinputtype',
};
const financial_taskinputtype_OriginalResponse: Coding = {
  code: 'origresponse',
  display: 'Original Response',
  system: 'http://terminology.hl7.org/CodeSystem/financialtaskinputtype',
};
const financial_taskinputtype_Period: Coding = {
  code: 'period',
  display: 'Period',
  system: 'http://terminology.hl7.org/CodeSystem/financialtaskinputtype',
};
const financial_taskinputtype_ReferenceNumber: Coding = {
  code: 'reference',
  display: 'Reference Number',
  system: 'http://terminology.hl7.org/CodeSystem/financialtaskinputtype',
};
const financial_taskinputtype_StatusCode: Coding = {
  code: 'status',
  display: 'Status code',
  system: 'http://terminology.hl7.org/CodeSystem/financialtaskinputtype',
};
/**
 * This value set includes Financial Task Input Type codes.
 */
export const FinancialTaskinputtype = {
  /**
   * The name of a resource to not include in a selection.
   */
  Exclude: financial_taskinputtype_Exclude,
  /**
   * The name of a resource to include in a selection.
   */
  Include: financial_taskinputtype_Include,
  /**
   * The sequence number associated with an item for reprocessing.
   */
  ItemNumber: financial_taskinputtype_ItemNumber,
  /**
   * A reference to the response resource to the original processing of a resource.
   */
  OriginalResponse: financial_taskinputtype_OriginalResponse,
  /**
   * The reference period for the action being requested.
   */
  Period: financial_taskinputtype_Period,
  /**
   * A reference value which must be quoted to authorize an action.
   */
  ReferenceNumber: financial_taskinputtype_ReferenceNumber,
  /**
   * The processing status from a check on the processing status of a resource such as the adjudication of a claim.
   */
  StatusCode: financial_taskinputtype_StatusCode,
};
const flag_category_Administrative: Coding = {
  code: 'admin',
  display: 'Administrative',
  system: 'http://terminology.hl7.org/CodeSystem/flag-category',
};
const flag_category_AdvanceDirective: Coding = {
  code: 'advance-directive',
  display: 'Advance Directive',
  system: 'http://terminology.hl7.org/CodeSystem/flag-category',
};
const flag_category_Behavioral: Coding = {
  code: 'behavioral',
  display: 'Behavioral',
  system: 'http://terminology.hl7.org/CodeSystem/flag-category',
};
const flag_category_Clinical: Coding = {
  code: 'clinical',
  display: 'Clinical',
  system: 'http://terminology.hl7.org/CodeSystem/flag-category',
};
const flag_category_SubjectContact: Coding = {
  code: 'contact',
  display: 'Subject Contact',
  system: 'http://terminology.hl7.org/CodeSystem/flag-category',
};
const flag_category_Diet: Coding = {
  code: 'diet',
  display: 'Diet',
  system: 'http://terminology.hl7.org/CodeSystem/flag-category',
};
const flag_category_Drug: Coding = {
  code: 'drug',
  display: 'Drug',
  system: 'http://terminology.hl7.org/CodeSystem/flag-category',
};
const flag_category_Lab: Coding = {
  code: 'lab',
  display: 'Lab',
  system: 'http://terminology.hl7.org/CodeSystem/flag-category',
};
const flag_category_Research: Coding = {
  code: 'research',
  display: 'Research',
  system: 'http://terminology.hl7.org/CodeSystem/flag-category',
};
const flag_category_Safety: Coding = {
  code: 'safety',
  display: 'Safety',
  system: 'http://terminology.hl7.org/CodeSystem/flag-category',
};
/**
 * Example list of general categories for flagged issues. (Not complete or necessarily appropriate.)
 */
export const FlagCategory = {
  /**
   * Flags related to administrative and financial processes.
   */
  Administrative: flag_category_Administrative,
  /**
   * Flags related to subject's advance directives.
   */
  AdvanceDirective: flag_category_AdvanceDirective,
  /**
   * Flags related to behavior.
   */
  Behavioral: flag_category_Behavioral,
  /**
   * Flags related to the subject's clinical data.
   */
  Clinical: flag_category_Clinical,
  /**
   * Flags related to coming into contact with the patient.
   */
  SubjectContact: flag_category_SubjectContact,
  /**
   * Flags related to the subject's dietary needs.
   */
  Diet: flag_category_Diet,
  /**
   * Flags related to the subject's medications.
   */
  Drug: flag_category_Drug,
  /**
   * Flags related to performing laboratory tests and related processes (e.g. phlebotomy).
   */
  Lab: flag_category_Lab,
  /**
   * Flags related to research.
   */
  Research: flag_category_Research,
  /**
   * Flags related to safety precautions.
   */
  Safety: flag_category_Safety,
};
const flag_priority_HighPriority: Coding = {
  code: 'PH',
  display: 'High priority',
  system: 'http://hl7.org/fhir/flag-priority-code',
};
const flag_priority_LowPriority: Coding = {
  code: 'PL',
  display: 'Low priority',
  system: 'http://hl7.org/fhir/flag-priority-code',
};
const flag_priority_MediumPriority: Coding = {
  code: 'PM',
  display: 'Medium priority',
  system: 'http://hl7.org/fhir/flag-priority-code',
};
const flag_priority_NoAlarm: Coding = {
  code: 'PN',
  display: 'No alarm',
  system: 'http://hl7.org/fhir/flag-priority-code',
};
/**
 * This value set is provided as an exemplar. The value set is driven by IHE Table B.8-4: Abnormal Flags, Alert Priority.
 */
export const FlagPriority = {
  /**
   * High priority.
   */
  HighPriority: flag_priority_HighPriority,
  /**
   * Low priority.
   */
  LowPriority: flag_priority_LowPriority,
  /**
   * Medium priority.
   */
  MediumPriority: flag_priority_MediumPriority,
  /**
   * No alarm.
   */
  NoAlarm: flag_priority_NoAlarm,
};
const flag_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/flag-status',
};
const flag_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/flag-status',
};
const flag_status_Inactive: Coding = {
  code: 'inactive',
  display: 'Inactive',
  system: 'http://hl7.org/fhir/flag-status',
};
/**
 * Indicates whether this flag is active and needs to be displayed to a user, or whether it is no longer needed or was entered in error.
 */
export const FlagStatus = {
  /**
   * A current flag that should be displayed to a user. A system may use the category to determine which user roles should view the flag.
   */
  Active: flag_status_Active,
  /**
   * The flag was added in error and should no longer be displayed.
   */
  EnteredInError: flag_status_EnteredInError,
  /**
   * The flag no longer needs to be displayed.
   */
  Inactive: flag_status_Inactive,
};
const fm_conditions_Headache: Coding = {
  code: '123987',
  display: 'Headache',
  system: 'http://hl7.org/fhir/fm-conditions',
};
/**
 * This value set includes sample Conditions codes.
 */
export const FmConditions = {
  /**
   * Headache
   */
  Headache: fm_conditions_Headache,
};
const fm_itemtype_Group: Coding = {
  code: 'group',
  display: 'Group',
  system: 'http://hl7.org/fhir/ex-claimitemtype',
};
const fm_itemtype_Product: Coding = {
  code: 'product',
  display: 'Product',
  system: 'http://hl7.org/fhir/ex-claimitemtype',
};
const fm_itemtype_Service: Coding = {
  code: 'service',
  display: 'Service',
  system: 'http://hl7.org/fhir/ex-claimitemtype',
};
/**
 * This value set includes sample Item Type codes.
 */
export const FmItemtype = {
  /**
   * A group of products and/or Services, amount ar the summary or detail level products and services.
   */
  Group: fm_itemtype_Group,
  /**
   * A billed product line item.
   */
  Product: fm_itemtype_Product,
  /**
   * A billed service line item.
   */
  Service: fm_itemtype_Service,
};
const fm_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/fm-status',
};
const fm_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/fm-status',
};
const fm_status_Draft: Coding = {
  code: 'draft',
  display: 'Draft',
  system: 'http://hl7.org/fhir/fm-status',
};
const fm_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/fm-status',
};
/**
 * This value set includes Status codes.
 */
export const FmStatus = {
  /**
   * The instance is currently in-force.
   */
  Active: fm_status_Active,
  /**
   * The instance is withdrawn, rescinded or reversed.
   */
  Cancelled: fm_status_Cancelled,
  /**
   * A new instance the contents of which is not complete.
   */
  Draft: fm_status_Draft,
  /**
   * The instance was entered in error.
   */
  EnteredInError: fm_status_EnteredInError,
};
const FocalSubject_Fetus: Coding = {
  code: '83418008',
  display: 'Fetus',
  system: 'http://snomed.info/sct',
};
const v3_ParticipationType_Donor: Coding = {
  code: 'DON',
  display: 'donor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_RoleCode_Spouse: Coding = {
  code: 'SPS',
  display: 'spouse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
/**
 * Example value set composed from SNOMED CT and HL7 V3 codes for observation targets such as donor, fetus or spouse. As use cases are discovered, more values may be added.
 */
export const FocalSubject = {
  Fetus_: FocalSubject_Fetus,
  /**
   * In some organ transplantation services and rarely in transfusion services a donor will be a target participant in the service.  However, in most cases transplantation is decomposed in three services: explantation, transport, and implantation.  The identity of the donor (recipient) is often irrelevant for the explantation (implantation) service.
   */
  Donor_v3_ParticipationType: v3_ParticipationType_Donor,
  /**
   * The player of the role is a marriage partner of the scoping person.
   */
  Spouse_v3_RoleCode: v3_RoleCode_Spouse,
};
const forms_FormNumber1: Coding = {
  code: '1',
  display: 'Form #1',
  system: 'http://terminology.hl7.org/CodeSystem/forms-codes',
};
const forms_FormNumber1_2: Coding = {
  code: '2',
  display: 'Form #1',
  system: 'http://terminology.hl7.org/CodeSystem/forms-codes',
};
/**
 * This value set includes a sample set of Forms codes.
 */
export const Forms = {
  /**
   * Example: The #1 form to be used when printing this information.
   */
  FormNumber1: forms_FormNumber1,
  /**
   * Example: The #2 form to be used when printing this information.
   */
  FormNumber1_2: forms_FormNumber1_2,
};
const fundsreserve_None: Coding = {
  code: 'none',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/fundsreserve',
};
const fundsreserve_Patient: Coding = {
  code: 'patient',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/fundsreserve',
};
const fundsreserve_Provider: Coding = {
  code: 'provider',
  display: 'Provider',
  system: 'http://terminology.hl7.org/CodeSystem/fundsreserve',
};
/**
 * This value set includes sample funds reservation type codes.
 */
export const Fundsreserve = {
  /**
   * The payor is not being requested to reserve any funds for the settlement of future claims.
   */
  None: fundsreserve_None,
  /**
   * The payor is requested to reserve funds for the provision of the named services by any provider for settlement of future claims related to this request.
   */
  Patient: fundsreserve_Patient,
  /**
   * The payor is requested to reserve funds solely for the named provider for settlement of future claims related to this request.
   */
  Provider: fundsreserve_Provider,
};
const gender_identity_Female: Coding = {
  code: 'female',
  display: 'female',
  system: 'http://hl7.org/fhir/gender-identity',
};
const gender_identity_Male: Coding = {
  code: 'male',
  display: 'male',
  system: 'http://hl7.org/fhir/gender-identity',
};
const gender_identity_NonBinary: Coding = {
  code: 'non-binary',
  display: 'non-binary',
  system: 'http://hl7.org/fhir/gender-identity',
};
const gender_identity_DoesNotWishToDisclose: Coding = {
  code: 'non-disclose',
  display: 'does not wish to disclose',
  system: 'http://hl7.org/fhir/gender-identity',
};
const gender_identity_Other: Coding = {
  code: 'other',
  display: 'other',
  system: 'http://hl7.org/fhir/gender-identity',
};
const gender_identity_TransgenderFemale: Coding = {
  code: 'transgender-female',
  display: 'transgender female',
  system: 'http://hl7.org/fhir/gender-identity',
};
const gender_identity_TransgenderMale: Coding = {
  code: 'transgender-male',
  display: 'transgender male',
  system: 'http://hl7.org/fhir/gender-identity',
};
/**
 * This example value set defines a set of codes that can be used to indicate a patient's gender identity.
 */
export const GenderIdentity = {
  /**
   * the patient identifies as female
   */
  Female: gender_identity_Female,
  /**
   * the patient identifies as male
   */
  Male: gender_identity_Male,
  /**
   * the patient identifies with neither/both female and male
   */
  NonBinary: gender_identity_NonBinary,
  /**
   * the patient does not wish to disclose his gender identity
   */
  DoesNotWishToDisclose: gender_identity_DoesNotWishToDisclose,
  /**
   * other gender identity
   */
  Other: gender_identity_Other,
  /**
   * the patient identifies as transgender male-to-female
   */
  TransgenderFemale: gender_identity_TransgenderFemale,
  /**
   * the patient identifies as transgender female-to-male
   */
  TransgenderMale: gender_identity_TransgenderMale,
};
const goal_acceptance_status_Agree: Coding = {
  code: 'agree',
  display: 'Agree',
  system: 'http://terminology.hl7.org/CodeSystem/goal-acceptance-status',
};
const goal_acceptance_status_Disagree: Coding = {
  code: 'disagree',
  display: 'Disagree',
  system: 'http://terminology.hl7.org/CodeSystem/goal-acceptance-status',
};
const goal_acceptance_status_Pending: Coding = {
  code: 'pending',
  display: 'Pending',
  system: 'http://terminology.hl7.org/CodeSystem/goal-acceptance-status',
};
/**
 * Codes indicating whether the goal has been accepted by a stakeholder.
 */
export const GoalAcceptanceStatus = {
  /**
   * Stakeholder supports pursuit of the goal.
   */
  Agree: goal_acceptance_status_Agree,
  /**
   * Stakeholder is not in support of the pursuit of the goal.
   */
  Disagree: goal_acceptance_status_Disagree,
  /**
   * Stakeholder has not yet made a decision on whether they support the goal.
   */
  Pending: goal_acceptance_status_Pending,
};
const goal_achievement_Achieved: Coding = {
  code: 'achieved',
  display: 'Achieved',
  system: 'http://terminology.hl7.org/CodeSystem/goal-achievement',
};
const goal_achievement_Improving: Coding = {
  code: 'improving',
  display: 'Improving',
  system: 'http://terminology.hl7.org/CodeSystem/goal-achievement',
};
const goal_achievement_InProgress: Coding = {
  code: 'in-progress',
  display: 'In Progress',
  system: 'http://terminology.hl7.org/CodeSystem/goal-achievement',
};
const goal_achievement_NoChange: Coding = {
  code: 'no-change',
  display: 'No Change',
  system: 'http://terminology.hl7.org/CodeSystem/goal-achievement',
};
const goal_achievement_NoProgress: Coding = {
  code: 'no-progress',
  display: 'No Progress',
  system: 'http://terminology.hl7.org/CodeSystem/goal-achievement',
};
const goal_achievement_NotAchieved: Coding = {
  code: 'not-achieved',
  display: 'Not Achieved',
  system: 'http://terminology.hl7.org/CodeSystem/goal-achievement',
};
const goal_achievement_NotAttainable: Coding = {
  code: 'not-attainable',
  display: 'Not Attainable',
  system: 'http://terminology.hl7.org/CodeSystem/goal-achievement',
};
const goal_achievement_Sustaining: Coding = {
  code: 'sustaining',
  display: 'Sustaining',
  system: 'http://terminology.hl7.org/CodeSystem/goal-achievement',
};
const goal_achievement_Worsening: Coding = {
  code: 'worsening',
  display: 'Worsening',
  system: 'http://terminology.hl7.org/CodeSystem/goal-achievement',
};
/**
 * Describes the progression, or lack thereof, towards the goal against the target.
 */
export const GoalAchievement = {
  /**
   * The goal has been met.
   */
  Achieved: goal_achievement_Achieved,
  /**
   * The goal is being sought, and is progressing.
   */
  Improving: goal_achievement_Improving,
  /**
   * The goal is being sought but has not yet been reached. (Also applies if the goal was reached in the past but there has been regression and the goal is again being sought).
   */
  InProgress: goal_achievement_InProgress,
  /**
   * The goal is being sought, but the trend is flat.
   */
  NoChange: goal_achievement_NoChange,
  /**
   * The goal has not been met and little to no progress towards target.
   */
  NoProgress: goal_achievement_NoProgress,
  /**
   * The goal has not been met and there might or might not have been progress towards target.
   */
  NotAchieved: goal_achievement_NotAchieved,
  /**
   * The goal is not possible to be met.
   */
  NotAttainable: goal_achievement_NotAttainable,
  /**
   * The goal has been met, but ongoing activity is needed to sustain the goal objective.
   */
  Sustaining: goal_achievement_Sustaining,
  /**
   * The goal is being sought, but is regressing.
   */
  Worsening: goal_achievement_Worsening,
};
const goal_category_Behavioral: Coding = {
  code: 'behavioral',
  display: 'Behavioral',
  system: 'http://terminology.hl7.org/CodeSystem/goal-category',
};
const goal_category_Dietary: Coding = {
  code: 'dietary',
  display: 'Dietary',
  system: 'http://terminology.hl7.org/CodeSystem/goal-category',
};
const goal_category_Nursing: Coding = {
  code: 'nursing',
  display: 'Nursing',
  system: 'http://terminology.hl7.org/CodeSystem/goal-category',
};
const goal_category_Physiotherapy: Coding = {
  code: 'physiotherapy',
  display: 'Physiotherapy',
  system: 'http://terminology.hl7.org/CodeSystem/goal-category',
};
const goal_category_Safety: Coding = {
  code: 'safety',
  display: 'Safety',
  system: 'http://terminology.hl7.org/CodeSystem/goal-category',
};
/**
 * Example codes for grouping goals to use for filtering or presentation.
 */
export const GoalCategory = {
  /**
   * Goals related to the manner in which the subject acts.
   */
  Behavioral: goal_category_Behavioral,
  /**
   * Goals related to the consumption of food and/or beverages.
   */
  Dietary: goal_category_Dietary,
  /**
   * Goals related to the practice of nursing or established by nurses.
   */
  Nursing: goal_category_Nursing,
  /**
   * Goals related to the mobility and/or motor capability of the subject.
   */
  Physiotherapy: goal_category_Physiotherapy,
  /**
   * Goals related to the personal protection of the subject.
   */
  Safety: goal_category_Safety,
};
const goal_priority_HighPriority: Coding = {
  code: 'high-priority',
  display: 'High Priority',
  system: 'http://terminology.hl7.org/CodeSystem/goal-priority',
};
const goal_priority_LowPriority: Coding = {
  code: 'low-priority',
  display: 'Low Priority',
  system: 'http://terminology.hl7.org/CodeSystem/goal-priority',
};
const goal_priority_MediumPriority: Coding = {
  code: 'medium-priority',
  display: 'Medium Priority',
  system: 'http://terminology.hl7.org/CodeSystem/goal-priority',
};
/**
 * Indicates the level of importance associated with reaching or sustaining a goal.
 */
export const GoalPriority = {
  /**
   * Indicates that the goal is of considerable importance and should be a primary focus of care delivery.
   */
  HighPriority: goal_priority_HighPriority,
  /**
   * The goal is desirable but is not sufficiently important to devote significant resources to.  Achievement of the goal may be sought when incidental to achieving other goals.
   */
  LowPriority: goal_priority_LowPriority,
  /**
   * Indicates that the goal has a reasonable degree of importance and that concrete action should be taken towards the goal.  Attainment is not as critical as high-priority goals.
   */
  MediumPriority: goal_priority_MediumPriority,
};
const goal_relationship_type_Milestone: Coding = {
  code: 'milestone',
  display: 'Milestone',
  system: 'http://terminology.hl7.org/CodeSystem/goal-relationship-type',
};
const goal_relationship_type_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/goal-relationship-type',
};
const goal_relationship_type_Predecessor: Coding = {
  code: 'predecessor',
  display: 'Predecessor',
  system: 'http://terminology.hl7.org/CodeSystem/goal-relationship-type',
};
const goal_relationship_type_Replacement: Coding = {
  code: 'replacement',
  display: 'Replacement',
  system: 'http://terminology.hl7.org/CodeSystem/goal-relationship-type',
};
const goal_relationship_type_Successor: Coding = {
  code: 'successor',
  display: 'Successor',
  system: 'http://terminology.hl7.org/CodeSystem/goal-relationship-type',
};
/**
 * Types of relationships between two goals.
 */
export const GoalRelationshipType = {
  /**
   * Indicates that the target goal is considered to be a "piece" of attaining this goal.
   */
  Milestone: goal_relationship_type_Milestone,
  /**
   * Indicates that the relationship is not covered by one of the pre-defined codes.  (An extension may convey more information about the meaning of the relationship.).
   */
  Other: goal_relationship_type_Other,
  /**
   * Indicates that the target goal is one which must be met before striving for the current goal.
   */
  Predecessor: goal_relationship_type_Predecessor,
  /**
   * Indicates that this goal has been replaced by the target goal.
   */
  Replacement: goal_relationship_type_Replacement,
  /**
   * Indicates that the target goal is a desired objective once the current goal is met.
   */
  Successor: goal_relationship_type_Successor,
};
const GoalStartEvent_DischargeFromHospital: Coding = {
  code: '308283009',
  display: 'Discharge from hospital',
  system: 'http://snomed.info/sct',
};
const GoalStartEvent_AdmissionToHospital: Coding = {
  code: '32485007',
  display: 'Admission to hospital',
  system: 'http://snomed.info/sct',
};
const GoalStartEvent_Childbirth: Coding = {
  code: '386216000',
  display: 'Childbirth',
  system: 'http://snomed.info/sct',
};
const GoalStartEvent_CompletionTimeOfProcedure: Coding = {
  code: '442137000',
  display: 'Completion time of procedure',
  system: 'http://snomed.info/sct',
};
/**
 * Identifies types of events that might trigger the start of a goal.
 */
export const GoalStartEvent = {
  DischargeFromHospital: GoalStartEvent_DischargeFromHospital,
  AdmissionToHospital: GoalStartEvent_AdmissionToHospital,
  Childbirth: GoalStartEvent_Childbirth,
  CompletionTimeOfProcedure: GoalStartEvent_CompletionTimeOfProcedure,
};
const goal_status_Accepted: Coding = {
  code: 'accepted',
  display: 'Accepted',
  system: 'http://hl7.org/fhir/goal-status',
};
const goal_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/goal-status',
};
const goal_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/goal-status',
};
const goal_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/goal-status',
};
const goal_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/goal-status',
};
const goal_status_OnHold: Coding = {
  code: 'on-hold',
  display: 'On Hold',
  system: 'http://hl7.org/fhir/goal-status',
};
const goal_status_Planned: Coding = {
  code: 'planned',
  display: 'Planned',
  system: 'http://hl7.org/fhir/goal-status',
};
const goal_status_Proposed: Coding = {
  code: 'proposed',
  display: 'Proposed',
  system: 'http://hl7.org/fhir/goal-status',
};
const goal_status_Rejected: Coding = {
  code: 'rejected',
  display: 'Rejected',
  system: 'http://hl7.org/fhir/goal-status',
};
/**
 * Codes that reflect the current state of a goal and whether the goal is still being targeted.
 */
export const GoalStatus = {
  /**
   * A proposed goal was accepted or acknowledged.
   */
  Accepted: goal_status_Accepted,
  /**
   * The goal is being sought actively.
   */
  Active: goal_status_Active,
  /**
   * The goal has been abandoned.
   */
  Cancelled: goal_status_Cancelled,
  /**
   * The goal is no longer being sought.
   */
  Completed: goal_status_Completed,
  /**
   * The goal was entered in error and voided.
   */
  EnteredInError: goal_status_EnteredInError,
  /**
   * The goal remains a long term objective but is no longer being actively pursued for a temporary period of time.
   */
  OnHold: goal_status_OnHold,
  /**
   * A goal is planned for this patient.
   */
  Planned: goal_status_Planned,
  /**
   * A goal is proposed for this patient.
   */
  Proposed: goal_status_Proposed,
  /**
   * A proposed goal was rejected.
   */
  Rejected: goal_status_Rejected,
};
const goal_status_reason_FinancialReason: Coding = {
  code: 'financial-barrier',
  display: 'Financial Reason',
  system: 'http://hl7.org/fhir/goal-status-reason',
};
const goal_status_reason_LackOfSocialSupport: Coding = {
  code: 'lack-of-social-support',
  display: 'Lack Of Social Support',
  system: 'http://hl7.org/fhir/goal-status-reason',
};
const goal_status_reason_LackOfTransportation: Coding = {
  code: 'lack-of-transportation',
  display: 'Lack Of Transportation',
  system: 'http://hl7.org/fhir/goal-status-reason',
};
const goal_status_reason_LifeEvent: Coding = {
  code: 'life-event',
  display: 'Life Event',
  system: 'http://hl7.org/fhir/goal-status-reason',
};
const goal_status_reason_PatientRequest: Coding = {
  code: 'patient-request',
  display: 'Patient Request',
  system: 'http://hl7.org/fhir/goal-status-reason',
};
const goal_status_reason_GoalNotAttainablePermanently: Coding = {
  code: 'permanent-not-attainable',
  display: 'Goal Not Attainable Permanently',
  system: 'http://hl7.org/fhir/goal-status-reason',
};
const goal_status_reason_Replaced: Coding = {
  code: 'replaced',
  display: 'Replaced',
  system: 'http://hl7.org/fhir/goal-status-reason',
};
const goal_status_reason_Surgery: Coding = {
  code: 'surgery',
  display: 'Surgery',
  system: 'http://hl7.org/fhir/goal-status-reason',
};
const goal_status_reason_GoalNotAttainableTemporarily: Coding = {
  code: 'temp-not-attainable',
  display: 'Goal Not Attainable Temporarily',
  system: 'http://hl7.org/fhir/goal-status-reason',
};
/**
 * Example codes indicating the reason for a current status.  Note that these are in no way complete and might not even be appropriate for some uses.
 */
export const GoalStatusReason = {
  /**
   * Goal cannot be reached due to financial barrier or reason.
   */
  FinancialReason: goal_status_reason_FinancialReason,
  /**
   * Goal cannot be reached due to a lack of social support.
   */
  LackOfSocialSupport: goal_status_reason_LackOfSocialSupport,
  /**
   * Goal cannot be reached due to a lack of transportation.
   */
  LackOfTransportation: goal_status_reason_LackOfTransportation,
  /**
   * Goal suspended or ended because of a significant life event (marital change, bereavement, etc.).
   */
  LifeEvent: goal_status_reason_LifeEvent,
  /**
   * Patient wishes the goal to be set aside, at least temporarily.
   */
  PatientRequest: goal_status_reason_PatientRequest,
  /**
   * Goal cannot be reached permanently.
   */
  GoalNotAttainablePermanently: goal_status_reason_GoalNotAttainablePermanently,
  /**
   * Goal has been superseded by a new goal.
   */
  Replaced: goal_status_reason_Replaced,
  /**
   * Goal suspended or ended because of a surgical procedure.
   */
  Surgery: goal_status_reason_Surgery,
  /**
   * Goal cannot be reached temporarily.
   */
  GoalNotAttainableTemporarily: goal_status_reason_GoalNotAttainableTemporarily,
};
const graph_compartment_rule_Custom: Coding = {
  code: 'custom',
  display: 'Custom',
  system: 'http://hl7.org/fhir/graph-compartment-rule',
};
const graph_compartment_rule_Different: Coding = {
  code: 'different',
  display: 'Different',
  system: 'http://hl7.org/fhir/graph-compartment-rule',
};
const graph_compartment_rule_Identical: Coding = {
  code: 'identical',
  display: 'Identical',
  system: 'http://hl7.org/fhir/graph-compartment-rule',
};
const graph_compartment_rule_Matching: Coding = {
  code: 'matching',
  display: 'Matching',
  system: 'http://hl7.org/fhir/graph-compartment-rule',
};
/**
 * How a compartment must be linked.
 */
export const GraphCompartmentRule = {
  /**
   * The compartment rule is defined in the accompanying FHIRPath expression.
   */
  Custom: graph_compartment_rule_Custom,
  /**
   * The compartment must be different.
   */
  Different: graph_compartment_rule_Different,
  /**
   * The compartment must be identical (the same literal reference).
   */
  Identical: graph_compartment_rule_Identical,
  /**
   * The compartment must be the same - the record must be about the same patient, but the reference may be different.
   */
  Matching: graph_compartment_rule_Matching,
};
const graph_compartment_use_Condition: Coding = {
  code: 'condition',
  display: 'Condition',
  system: 'http://hl7.org/fhir/graph-compartment-use',
};
const graph_compartment_use_Requirement: Coding = {
  code: 'requirement',
  display: 'Requirement',
  system: 'http://hl7.org/fhir/graph-compartment-use',
};
/**
 * Defines how a compartment rule is used.
 */
export const GraphCompartmentUse = {
  /**
   * This compartment rule is a condition for whether the rule applies.
   */
  Condition: graph_compartment_use_Condition,
  /**
   * This compartment rule is enforced on any relationships that meet the conditions.
   */
  Requirement: graph_compartment_use_Requirement,
};
const group_measure_Mean: Coding = {
  code: 'mean',
  display: 'Mean',
  system: 'http://hl7.org/fhir/group-measure',
};
const group_measure_MeanOfStudyMeans: Coding = {
  code: 'mean-of-mean',
  display: 'Mean of Study Means',
  system: 'http://hl7.org/fhir/group-measure',
};
const group_measure_MeanOfStudyMedins: Coding = {
  code: 'mean-of-median',
  display: 'Mean of Study Medins',
  system: 'http://hl7.org/fhir/group-measure',
};
const group_measure_Median: Coding = {
  code: 'median',
  display: 'Median',
  system: 'http://hl7.org/fhir/group-measure',
};
const group_measure_MedianOfStudyMeans: Coding = {
  code: 'median-of-mean',
  display: 'Median of Study Means',
  system: 'http://hl7.org/fhir/group-measure',
};
const group_measure_MedianOfStudyMedians: Coding = {
  code: 'median-of-median',
  display: 'Median of Study Medians',
  system: 'http://hl7.org/fhir/group-measure',
};
/**
 * Possible group measure aggregates (E.g. Mean, Median).
 */
export const GroupMeasure = {
  /**
   * Aggregated using Mean of participant values.
   */
  Mean: group_measure_Mean,
  /**
   * Aggregated using Mean of study mean values.
   */
  MeanOfStudyMeans: group_measure_MeanOfStudyMeans,
  /**
   * Aggregated using Mean of study median values.
   */
  MeanOfStudyMedins: group_measure_MeanOfStudyMedins,
  /**
   * Aggregated using Median of participant values.
   */
  Median: group_measure_Median,
  /**
   * Aggregated using Median of study mean values.
   */
  MedianOfStudyMeans: group_measure_MedianOfStudyMeans,
  /**
   * Aggregated using Median of study median values.
   */
  MedianOfStudyMedians: group_measure_MedianOfStudyMedians,
};
const group_type_Animal: Coding = {
  code: 'animal',
  display: 'Animal',
  system: 'http://hl7.org/fhir/group-type',
};
const group_type_Device: Coding = {
  code: 'device',
  display: 'Device',
  system: 'http://hl7.org/fhir/group-type',
};
const group_type_Medication: Coding = {
  code: 'medication',
  display: 'Medication',
  system: 'http://hl7.org/fhir/group-type',
};
const group_type_Person: Coding = {
  code: 'person',
  display: 'Person',
  system: 'http://hl7.org/fhir/group-type',
};
const group_type_Practitioner: Coding = {
  code: 'practitioner',
  display: 'Practitioner',
  system: 'http://hl7.org/fhir/group-type',
};
const group_type_Substance: Coding = {
  code: 'substance',
  display: 'Substance',
  system: 'http://hl7.org/fhir/group-type',
};
/**
 * Types of resources that are part of group.
 */
export const GroupType = {
  /**
   * Group contains "animal" Patient resources.
   */
  Animal: group_type_Animal,
  /**
   * Group contains Device resources.
   */
  Device: group_type_Device,
  /**
   * Group contains Medication resources.
   */
  Medication: group_type_Medication,
  /**
   * Group contains "person" Patient resources.
   */
  Person: group_type_Person,
  /**
   * Group contains healthcare practitioner resources (Practitioner or PractitionerRole).
   */
  Practitioner: group_type_Practitioner,
  /**
   * Group contains Substance resources.
   */
  Substance: group_type_Substance,
};
const guidance_response_status_DataRequested: Coding = {
  code: 'data-requested',
  display: 'Data Requested',
  system: 'http://hl7.org/fhir/guidance-response-status',
};
const guidance_response_status_DataRequired: Coding = {
  code: 'data-required',
  display: 'Data Required',
  system: 'http://hl7.org/fhir/guidance-response-status',
};
const guidance_response_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered In Error',
  system: 'http://hl7.org/fhir/guidance-response-status',
};
const guidance_response_status_Failure: Coding = {
  code: 'failure',
  display: 'Failure',
  system: 'http://hl7.org/fhir/guidance-response-status',
};
const guidance_response_status_InProgress: Coding = {
  code: 'in-progress',
  display: 'In Progress',
  system: 'http://hl7.org/fhir/guidance-response-status',
};
const guidance_response_status_Success: Coding = {
  code: 'success',
  display: 'Success',
  system: 'http://hl7.org/fhir/guidance-response-status',
};
/**
 * The status of a guidance response.
 */
export const GuidanceResponseStatus = {
  /**
   * The request was processed successfully, but more data may result in a more complete evaluation.
   */
  DataRequested: guidance_response_status_DataRequested,
  /**
   * The request was processed, but more data is required to complete the evaluation.
   */
  DataRequired: guidance_response_status_DataRequired,
  /**
   * The response was entered in error.
   */
  EnteredInError: guidance_response_status_EnteredInError,
  /**
   * The request was not processed successfully.
   */
  Failure: guidance_response_status_Failure,
  /**
   * The request is currently being processed.
   */
  InProgress: guidance_response_status_InProgress,
  /**
   * The request was processed successfully.
   */
  Success: guidance_response_status_Success,
};
const guide_page_generation_Generated: Coding = {
  code: 'generated',
  display: 'Generated',
  system: 'http://hl7.org/fhir/guide-page-generation',
};
const guide_page_generation_HTML: Coding = {
  code: 'html',
  display: 'HTML',
  system: 'http://hl7.org/fhir/guide-page-generation',
};
const guide_page_generation_Markdown: Coding = {
  code: 'markdown',
  display: 'Markdown',
  system: 'http://hl7.org/fhir/guide-page-generation',
};
const guide_page_generation_XML: Coding = {
  code: 'xml',
  display: 'XML',
  system: 'http://hl7.org/fhir/guide-page-generation',
};
/**
 * A code that indicates how the page is generated.
 */
export const GuidePageGeneration = {
  /**
   * Page will be generated by the publication process - no source to bring across.
   */
  Generated: guide_page_generation_Generated,
  /**
   * Page is proper xhtml with no templating.  Will be brought across unchanged for standard post-processing.
   */
  HTML: guide_page_generation_HTML,
  /**
   * Page is markdown with templating.  Will use the template to create a file that imports the markdown file prior to post-processing.
   */
  Markdown: guide_page_generation_Markdown,
  /**
   * Page is xml with templating.  Will use the template to create a file that imports the source file and run the nominated XSLT transform (see parameters) if present prior to post-processing.
   */
  XML: guide_page_generation_XML,
};
const guide_parameter_code_ApplyMetadataValue: Coding = {
  code: 'apply',
  display: 'Apply Metadata Value',
  system: 'http://hl7.org/fhir/guide-parameter-code',
};
const guide_parameter_code_ExpansionProfile: Coding = {
  code: 'expansion-parameter',
  display: 'Expansion Profile',
  system: 'http://hl7.org/fhir/guide-parameter-code',
};
const guide_parameter_code_GenerateJSON: Coding = {
  code: 'generate-json',
  display: 'Generate JSON',
  system: 'http://hl7.org/fhir/guide-parameter-code',
};
const guide_parameter_code_GenerateTurtle: Coding = {
  code: 'generate-turtle',
  display: 'Generate Turtle',
  system: 'http://hl7.org/fhir/guide-parameter-code',
};
const guide_parameter_code_GenerateXML: Coding = {
  code: 'generate-xml',
  display: 'Generate XML',
  system: 'http://hl7.org/fhir/guide-parameter-code',
};
const guide_parameter_code_HTMLTemplate: Coding = {
  code: 'html-template',
  display: 'HTML Template',
  system: 'http://hl7.org/fhir/guide-parameter-code',
};
const guide_parameter_code_PagesPath: Coding = {
  code: 'path-pages',
  display: 'Pages Path',
  system: 'http://hl7.org/fhir/guide-parameter-code',
};
const guide_parameter_code_ResourcePath: Coding = {
  code: 'path-resource',
  display: 'Resource Path',
  system: 'http://hl7.org/fhir/guide-parameter-code',
};
const guide_parameter_code_TerminologyCachePath: Coding = {
  code: 'path-tx-cache',
  display: 'Terminology Cache Path',
  system: 'http://hl7.org/fhir/guide-parameter-code',
};
const guide_parameter_code_BrokenLinksRule: Coding = {
  code: 'rule-broken-links',
  display: 'Broken Links Rule',
  system: 'http://hl7.org/fhir/guide-parameter-code',
};
/**
 * Code of parameter that is input to the guide.
 */
export const GuideParameterCode = {
  /**
   * If the value of this string 0..* parameter is one of the metadata fields then all conformance resources will have any specified [Resource].[field] overwritten with the ImplementationGuide.[field], where field is one of: version, date, status, publisher, contact, copyright, experimental, jurisdiction, useContext.
   */
  ApplyMetadataValue: guide_parameter_code_ApplyMetadataValue,
  /**
   * The value of this string 0..* parameter is a parameter (name=value) when expanding value sets for this implementation guide. This is particularly used to specify the versions of published terminologies such as SNOMED CT.
   */
  ExpansionProfile: guide_parameter_code_ExpansionProfile,
  /**
   * The value of this boolean 0..1 parameter specifies whether the IG publisher creates examples in JSON format. If not present, the Publication Tool decides whether to generate JSON.
   */
  GenerateJSON: guide_parameter_code_GenerateJSON,
  /**
   * The value of this boolean 0..1 parameter specifies whether the IG publisher creates examples in Turtle format. If not present, the Publication Tool decides whether to generate Turtle.
   */
  GenerateTurtle: guide_parameter_code_GenerateTurtle,
  /**
   * The value of this boolean 0..1 parameter specifies whether the IG publisher creates examples in XML format. If not present, the Publication Tool decides whether to generate XML.
   */
  GenerateXML: guide_parameter_code_GenerateXML,
  /**
   * The value of this string singleton parameter is the name of the file to use as the builder template for each generated page (see templating).
   */
  HTMLTemplate: guide_parameter_code_HTMLTemplate,
  /**
   * The value of this string 0..1 parameter is a subfolder of the build context's location that contains files that are part of the html content processed by the builder.
   */
  PagesPath: guide_parameter_code_PagesPath,
  /**
   * The value of this string 0..* parameter is a subfolder of the build context's location that is to be scanned to load resources. Scope is (if present) a particular resource type.
   */
  ResourcePath: guide_parameter_code_ResourcePath,
  /**
   * The value of this string 0..1 parameter is a subfolder of the build context's location that is used as the terminology cache. If this is not present, the terminology cache is on the local system, not under version control.
   */
  TerminologyCachePath: guide_parameter_code_TerminologyCachePath,
  /**
   * The value of this string 0..1 parameter is either "warning" or "error" (default = "error"). If the value is "warning" then IG build tools allow the IG to be considered successfully build even when there is no internal broken links.
   */
  BrokenLinksRule: guide_parameter_code_BrokenLinksRule,
};
const handling_condition_Frozen: Coding = {
  code: 'frozen',
  display: 'frozen',
  system: 'http://terminology.hl7.org/CodeSystem/handling-condition',
};
const handling_condition_Refrigerated: Coding = {
  code: 'refrigerated',
  display: 'refrigerated',
  system: 'http://terminology.hl7.org/CodeSystem/handling-condition',
};
const handling_condition_RoomTemperature: Coding = {
  code: 'room',
  display: 'room temperature',
  system: 'http://terminology.hl7.org/CodeSystem/handling-condition',
};
/**
 * Set of handling instructions prior testing of the specimen.
 */
export const HandlingCondition = {
  /**
   * frozen temperature.
   */
  Frozen: handling_condition_Frozen,
  /**
   * refrigerated temperature.
   */
  Refrigerated: handling_condition_Refrigerated,
  /**
   * room temperature.
   */
  RoomTemperature: handling_condition_RoomTemperature,
};
const history_absent_reason_Deferred: Coding = {
  code: 'deferred',
  display: 'Deferred',
  system: 'http://terminology.hl7.org/CodeSystem/history-absent-reason',
};
const history_absent_reason_SubjectUnknown: Coding = {
  code: 'subject-unknown',
  display: 'Subject Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/history-absent-reason',
};
const history_absent_reason_UnableToObtain: Coding = {
  code: 'unable-to-obtain',
  display: 'Unable To Obtain',
  system: 'http://terminology.hl7.org/CodeSystem/history-absent-reason',
};
const history_absent_reason_InformationWithheld: Coding = {
  code: 'withheld',
  display: 'Information Withheld',
  system: 'http://terminology.hl7.org/CodeSystem/history-absent-reason',
};
/**
 * Codes describing the reason why a family member's history is not available.
 */
export const HistoryAbsentReason = {
  /**
   * Patient does not have the information now, but can provide the information at a later date.
   */
  Deferred: history_absent_reason_Deferred,
  /**
   * Patient does not know the subject, e.g. the biological parent of an adopted patient.
   */
  SubjectUnknown: history_absent_reason_SubjectUnknown,
  /**
   * Information cannot be obtained; e.g. unconscious patient.
   */
  UnableToObtain: history_absent_reason_UnableToObtain,
  /**
   * The patient withheld or refused to share the information.
   */
  InformationWithheld: history_absent_reason_InformationWithheld,
};
const history_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/history-status',
};
const history_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/history-status',
};
const history_status_HealthUnknown: Coding = {
  code: 'health-unknown',
  display: 'Health Unknown',
  system: 'http://hl7.org/fhir/history-status',
};
const history_status_Partial: Coding = {
  code: 'partial',
  display: 'Partial',
  system: 'http://hl7.org/fhir/history-status',
};
/**
 * A code that identifies the status of the family history record.
 */
export const HistoryStatus = {
  /**
   * All available related health information is captured as of the date (and possibly time) when the family member history was taken.
   */
  Completed: history_status_Completed,
  /**
   * This instance should not have been part of this patient's medical record.
   */
  EnteredInError: history_status_EnteredInError,
  /**
   * Health information for this family member is unavailable/unknown.
   */
  HealthUnknown: history_status_HealthUnknown,
  /**
   * Some health information is known and captured, but not complete - see notes for details.
   */
  Partial: history_status_Partial,
};
const hl7_work_group_ApplicationImplementationAndDesign: Coding = {
  code: 'aid',
  display: 'Application Implementation and Design',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_BiomedicalResearchAndRegulation: Coding = {
  code: 'brr',
  display: 'Biomedical Research and Regulation',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_CommunityBasedCollaborativeCare: Coding = {
  code: 'cbcc',
  display: 'Community Based Collaborative Care',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_ClinicalDecisionSupport: Coding = {
  code: 'cds',
  display: 'Clinical Decision Support',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_ClinicalGenomics: Coding = {
  code: 'cg',
  display: 'Clinical Genomics',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_ClinicalQualityInformation: Coding = {
  code: 'cqi',
  display: 'Clinical Quality Information',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_HealthCareDevices: Coding = {
  code: 'dev',
  display: 'Health Care Devices',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_ElectronicHealthRecords: Coding = {
  code: 'ehr',
  display: 'Electronic Health Records',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_FHIRInfrastructure: Coding = {
  code: 'fhir',
  display: 'FHIR Infrastructure',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_FinancialManagement: Coding = {
  code: 'fm',
  display: 'Financial Management',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_HealthStandardsIntegration: Coding = {
  code: 'hsi',
  display: 'Health Standards Integration',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_ImagingIntegration: Coding = {
  code: 'ii',
  display: 'Imaging Integration',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_InfrastructureAndMessaging: Coding = {
  code: 'inm',
  display: 'Infrastructure And Messaging',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_ImplementableTechnologySpecifications: Coding = {
  code: 'its',
  display: 'Implementable Technology Specifications',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_ModelingAndMethodology: Coding = {
  code: 'mnm',
  display: 'Modeling and Methodology',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_OrdersAndObservations: Coding = {
  code: 'oo',
  display: 'Orders and Observations',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_PatientAdministration: Coding = {
  code: 'pa',
  display: 'Patient Administration',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_PatientCare: Coding = {
  code: 'pc',
  display: 'Patient Care',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_PublicHealthAndEmergencyResponse: Coding = {
  code: 'pher',
  display: 'Public Health and Emergency Response',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_Pharmacy: Coding = {
  code: 'phx',
  display: 'Pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_StructuredDocuments: Coding = {
  code: 'sd',
  display: 'Structured Documents',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_Security: Coding = {
  code: 'sec',
  display: 'Security',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_USRealmTaskforce: Coding = {
  code: 'us',
  display: 'US Realm Taskforce',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
const hl7_work_group_Vocabulary: Coding = {
  code: 'vocab',
  display: 'Vocabulary',
  system: 'http://terminology.hl7.org/CodeSystem/hl7-work-group',
};
/**
 * An HL7 administrative unit that owns artifacts in the FHIR specification.
 */
export const Hl7WorkGroup = {
  /**
   * Application Implementation and Design (http://www.hl7.org/Special/committees/java/index.cfm).
   */
  ApplicationImplementationAndDesign: hl7_work_group_ApplicationImplementationAndDesign,
  /**
   * Biomedical Research and Regulation (http://www.hl7.org/Special/committees/rcrim/index.cfm).
   */
  BiomedicalResearchAndRegulation: hl7_work_group_BiomedicalResearchAndRegulation,
  /**
   * Community Based Collaborative Care (http://www.hl7.org/Special/committees/cbcc/index.cfm).
   */
  CommunityBasedCollaborativeCare: hl7_work_group_CommunityBasedCollaborativeCare,
  /**
   * Clinical Decision Support (http://www.hl7.org/Special/committees/dss/index.cfm).
   */
  ClinicalDecisionSupport: hl7_work_group_ClinicalDecisionSupport,
  /**
   * Clinical Genomics (http://www.hl7.org/Special/committees/clingenomics/index.cfm).
   */
  ClinicalGenomics: hl7_work_group_ClinicalGenomics,
  /**
   * Clinical Quality Information (http://www.hl7.org/Special/committees/cqi/index.cfm).
   */
  ClinicalQualityInformation: hl7_work_group_ClinicalQualityInformation,
  /**
   * Health Care Devices (http://www.hl7.org/Special/committees/healthcaredevices/index.cfm).
   */
  HealthCareDevices: hl7_work_group_HealthCareDevices,
  /**
   * Electronic Health Records (http://www.hl7.org/special/committees/ehr/index.cfm).
   */
  ElectronicHealthRecords: hl7_work_group_ElectronicHealthRecords,
  /**
   * FHIR Infrastructure (http://www.hl7.org/Special/committees/fiwg/index.cfm).
   */
  FHIRInfrastructure: hl7_work_group_FHIRInfrastructure,
  /**
   * Financial Management (http://www.hl7.org/Special/committees/fm/index.cfm).
   */
  FinancialManagement: hl7_work_group_FinancialManagement,
  /**
   * Health Standards Integration (http://www.hl7.org/Special/committees/hsi/index.cfm).
   */
  HealthStandardsIntegration: hl7_work_group_HealthStandardsIntegration,
  /**
   * Imaging Integration (http://www.hl7.org/Special/committees/imagemgt/index.cfm).
   */
  ImagingIntegration: hl7_work_group_ImagingIntegration,
  /**
   * Infrastructure And Messaging (http://www.hl7.org/special/committees/inm/index.cfm).
   */
  InfrastructureAndMessaging: hl7_work_group_InfrastructureAndMessaging,
  /**
   * Implementable Technology Specifications (http://www.hl7.org/special/committees/xml/index.cfm).
   */
  ImplementableTechnologySpecifications: hl7_work_group_ImplementableTechnologySpecifications,
  /**
   * Modeling and Methodology (http://www.hl7.org/Special/committees/mnm/index.cfm).
   */
  ModelingAndMethodology: hl7_work_group_ModelingAndMethodology,
  /**
   * Orders and Observations (http://www.hl7.org/Special/committees/orders/index.cfm).
   */
  OrdersAndObservations: hl7_work_group_OrdersAndObservations,
  /**
   * Patient Administration (http://www.hl7.org/Special/committees/pafm/index.cfm).
   */
  PatientAdministration: hl7_work_group_PatientAdministration,
  /**
   * Patient Care (http://www.hl7.org/Special/committees/patientcare/index.cfm).
   */
  PatientCare: hl7_work_group_PatientCare,
  /**
   * Public Health and Emergency Response (http://www.hl7.org/Special/committees/pher/index.cfm).
   */
  PublicHealthAndEmergencyResponse: hl7_work_group_PublicHealthAndEmergencyResponse,
  /**
   * Pharmacy (http://www.hl7.org/Special/committees/medication/index.cfm).
   */
  Pharmacy: hl7_work_group_Pharmacy,
  /**
   * Structured Documents (http://www.hl7.org/Special/committees/structure/index.cfm).
   */
  StructuredDocuments: hl7_work_group_StructuredDocuments,
  /**
   * Security (http://www.hl7.org/Special/committees/secure/index.cfm).
   */
  Security: hl7_work_group_Security,
  /**
   * US Realm Taskforce (http://www.hl7.org/Special/committees/usrealm/index.cfm).
   */
  USRealmTaskforce: hl7_work_group_USRealmTaskforce,
  /**
   * Vocabulary (http://www.hl7.org/Special/committees/Vocab/index.cfm).
   */
  Vocabulary: hl7_work_group_Vocabulary,
};
const http_operations_DELETE: Coding = {
  code: 'delete',
  display: 'DELETE',
  system: 'http://hl7.org/fhir/http-operations',
};
const http_operations_GET: Coding = {
  code: 'get',
  display: 'GET',
  system: 'http://hl7.org/fhir/http-operations',
};
const http_operations_HEAD: Coding = {
  code: 'head',
  display: 'HEAD',
  system: 'http://hl7.org/fhir/http-operations',
};
const http_operations_OPTIONS: Coding = {
  code: 'options',
  display: 'OPTIONS',
  system: 'http://hl7.org/fhir/http-operations',
};
const http_operations_PATCH: Coding = {
  code: 'patch',
  display: 'PATCH',
  system: 'http://hl7.org/fhir/http-operations',
};
const http_operations_POST: Coding = {
  code: 'post',
  display: 'POST',
  system: 'http://hl7.org/fhir/http-operations',
};
const http_operations_PUT: Coding = {
  code: 'put',
  display: 'PUT',
  system: 'http://hl7.org/fhir/http-operations',
};
/**
 * The allowable request method or HTTP operation codes.
 */
export const HttpOperations = {
  /**
   * HTTP DELETE operation.
   */
  DELETE: http_operations_DELETE,
  /**
   * HTTP GET operation.
   */
  GET: http_operations_GET,
  /**
   * HTTP HEAD operation.
   */
  HEAD: http_operations_HEAD,
  /**
   * HTTP OPTIONS operation.
   */
  OPTIONS: http_operations_OPTIONS,
  /**
   * HTTP PATCH operation.
   */
  PATCH: http_operations_PATCH,
  /**
   * HTTP POST operation.
   */
  POST: http_operations_POST,
  /**
   * HTTP PUT operation.
   */
  PUT: http_operations_PUT,
};
const http_verb_DELETE: Coding = {
  code: 'DELETE',
  display: 'DELETE',
  system: 'http://hl7.org/fhir/http-verb',
};
const http_verb_GET: Coding = {
  code: 'GET',
  display: 'GET',
  system: 'http://hl7.org/fhir/http-verb',
};
const http_verb_HEAD: Coding = {
  code: 'HEAD',
  display: 'HEAD',
  system: 'http://hl7.org/fhir/http-verb',
};
const http_verb_PATCH: Coding = {
  code: 'PATCH',
  display: 'PATCH',
  system: 'http://hl7.org/fhir/http-verb',
};
const http_verb_POST: Coding = {
  code: 'POST',
  display: 'POST',
  system: 'http://hl7.org/fhir/http-verb',
};
const http_verb_PUT: Coding = {
  code: 'PUT',
  display: 'PUT',
  system: 'http://hl7.org/fhir/http-verb',
};
/**
 * HTTP verbs (in the HTTP command line). See [HTTP rfc](https://tools.ietf.org/html/rfc7231) for details.
 */
export const HttpVerb = {
  /**
   * HTTP DELETE Command.
   */
  DELETE: http_verb_DELETE,
  /**
   * HTTP GET Command.
   */
  GET: http_verb_GET,
  /**
   * HTTP HEAD Command.
   */
  HEAD: http_verb_HEAD,
  /**
   * HTTP PATCH Command.
   */
  PATCH: http_verb_PATCH,
  /**
   * HTTP POST Command.
   */
  POST: http_verb_POST,
  /**
   * HTTP PUT Command.
   */
  PUT: http_verb_PUT,
};
const sid_icd_10_DIAG4: Coding = {
  code: '112233',
  display: 'DIAG-4',
  system: 'http://hl7.org/fhir/sid/icd-10',
};
const sid_icd_10_DIAG1: Coding = {
  code: '123456',
  display: 'DIAG-1',
  system: 'http://hl7.org/fhir/sid/icd-10',
};
const sid_icd_10_DIAG1a: Coding = {
  code: '123457',
  display: 'DIAG-1a',
  system: 'http://hl7.org/fhir/sid/icd-10',
};
const sid_icd_10_DIAG3: Coding = {
  code: '123987',
  display: 'DIAG-3',
  system: 'http://hl7.org/fhir/sid/icd-10',
};
const sid_icd_10_DIAG6: Coding = {
  code: '321789',
  display: 'DIAG-6',
  system: 'http://hl7.org/fhir/sid/icd-10',
};
const sid_icd_10_DIAG2: Coding = {
  code: '987654',
  display: 'DIAG-2',
  system: 'http://hl7.org/fhir/sid/icd-10',
};
const sid_icd_10_DIAG5: Coding = {
  code: '997755',
  display: 'DIAG-5',
  system: 'http://hl7.org/fhir/sid/icd-10',
};
/**
 * This value set includes sample ICD-10 codes.
 */
export const Icd10 = {
  DIAG4: sid_icd_10_DIAG4,
  DIAG1: sid_icd_10_DIAG1,
  DIAG1a: sid_icd_10_DIAG1a,
  DIAG3: sid_icd_10_DIAG3,
  DIAG6: sid_icd_10_DIAG6,
  DIAG2: sid_icd_10_DIAG2,
  DIAG5: sid_icd_10_DIAG5,
};
const icd_10_procedures_PROC1: Coding = {
  code: '123001',
  display: 'PROC-1',
  system: 'http://hl7.org/fhir/sid/ex-icd-10-procedures',
};
const icd_10_procedures_PROC2: Coding = {
  code: '123002',
  display: 'PROC-2',
  system: 'http://hl7.org/fhir/sid/ex-icd-10-procedures',
};
const icd_10_procedures_PROC3: Coding = {
  code: '123003',
  display: 'PROC-3',
  system: 'http://hl7.org/fhir/sid/ex-icd-10-procedures',
};
/**
 * This value set includes sample ICD-10 Procedure codes.
 */
export const Icd10Procedures = {
  /**
   * Procedure 1
   */
  PROC1: icd_10_procedures_PROC1,
  /**
   * Procedure 2
   */
  PROC2: icd_10_procedures_PROC2,
  /**
   * Procedure 3
   */
  PROC3: icd_10_procedures_PROC3,
};
const v2_0203_AccessionID: Coding = {
  code: 'ACSN',
  display: 'Accession ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_BreedRegistryNumber: Coding = {
  code: 'BRN',
  display: 'Breed Registry Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_DriverQuoteSLicenseNumber: Coding = {
  code: 'DL',
  display: "Driver's license number",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_DonorRegistrationNumber: Coding = {
  code: 'DR',
  display: 'Donor Registration Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_EmployerNumber: Coding = {
  code: 'EN',
  display: 'Employer number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_FillerIdentifier: Coding = {
  code: 'FILL',
  display: 'Filler Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_JurisdictionalHealthNumberCanada: Coding = {
  code: 'JHN',
  display: 'Jurisdictional health number (Canada)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_MicrochipNumber: Coding = {
  code: 'MCN',
  display: 'Microchip Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_MedicalLicenseNumber: Coding = {
  code: 'MD',
  display: 'Medical License number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_MedicalRecordNumber: Coding = {
  code: 'MR',
  display: 'Medical record number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_NationalInsurancePayorIdentifierPayor: Coding = {
  code: 'NIIP',
  display: 'National Insurance Payor Identifier (Payor)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PlacerIdentifier: Coding = {
  code: 'PLAC',
  display: 'Placer Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PassportNumber: Coding = {
  code: 'PPN',
  display: 'Passport number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_ProviderNumber: Coding = {
  code: 'PRN',
  display: 'Provider number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_SocialBeneficiaryIdentifier: Coding = {
  code: 'SB',
  display: 'Social Beneficiary Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_SerialNumber: Coding = {
  code: 'SNO',
  display: 'Serial Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_TaxIDNumber: Coding = {
  code: 'TAX',
  display: 'Tax ID number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_UniversalDeviceIdentifier: Coding = {
  code: 'UDI',
  display: 'Universal Device Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
/**
 * A coded type for an identifier that can be used to determine which identifier to use for a specific purpose.
 */
export const IdentifierType = {
  AccessionID: v2_0203_AccessionID,
  BreedRegistryNumber: v2_0203_BreedRegistryNumber,
  DriverQuoteSLicenseNumber: v2_0203_DriverQuoteSLicenseNumber,
  DonorRegistrationNumber: v2_0203_DonorRegistrationNumber,
  EmployerNumber: v2_0203_EmployerNumber,
  FillerIdentifier: v2_0203_FillerIdentifier,
  JurisdictionalHealthNumberCanada: v2_0203_JurisdictionalHealthNumberCanada,
  MicrochipNumber: v2_0203_MicrochipNumber,
  MedicalLicenseNumber: v2_0203_MedicalLicenseNumber,
  MedicalRecordNumber: v2_0203_MedicalRecordNumber,
  NationalInsurancePayorIdentifierPayor: v2_0203_NationalInsurancePayorIdentifierPayor,
  PlacerIdentifier: v2_0203_PlacerIdentifier,
  PassportNumber: v2_0203_PassportNumber,
  ProviderNumber: v2_0203_ProviderNumber,
  SocialBeneficiaryIdentifier: v2_0203_SocialBeneficiaryIdentifier,
  SerialNumber: v2_0203_SerialNumber,
  TaxIDNumber: v2_0203_TaxIDNumber,
  UniversalDeviceIdentifier: v2_0203_UniversalDeviceIdentifier,
};
const identifier_use_Official: Coding = {
  code: 'official',
  display: 'Official',
  system: 'http://hl7.org/fhir/identifier-use',
};
const identifier_use_Old: Coding = {
  code: 'old',
  display: 'Old',
  system: 'http://hl7.org/fhir/identifier-use',
};
const identifier_use_Secondary: Coding = {
  code: 'secondary',
  display: 'Secondary',
  system: 'http://hl7.org/fhir/identifier-use',
};
const identifier_use_Temp: Coding = {
  code: 'temp',
  display: 'Temp',
  system: 'http://hl7.org/fhir/identifier-use',
};
const identifier_use_Usual: Coding = {
  code: 'usual',
  display: 'Usual',
  system: 'http://hl7.org/fhir/identifier-use',
};
/**
 * Identifies the purpose for this identifier, if known .
 */
export const IdentifierUse = {
  /**
   * The identifier considered to be most trusted for the identification of this item. Sometimes also known as "primary" and "main". The determination of "official" is subjective and implementation guides often provide additional guidelines for use.
   */
  Official: identifier_use_Official,
  /**
   * The identifier id no longer considered valid, but may be relevant for search purposes.  E.g. Changes to identifier schemes, account merges, etc.
   */
  Old: identifier_use_Old,
  /**
   * An identifier that was assigned in secondary use - it serves to identify the object in a relative context, but cannot be consistently assigned to the same object again in a different context.
   */
  Secondary: identifier_use_Secondary,
  /**
   * A temporary identifier.
   */
  Temp: identifier_use_Temp,
  /**
   * The identifier recommended for display and use in real-world interactions.
   */
  Usual: identifier_use_Usual,
};
const identity_assuranceLevel_Level1: Coding = {
  code: 'level1',
  display: 'Level 1',
  system: 'http://hl7.org/fhir/identity-assuranceLevel',
};
const identity_assuranceLevel_Level2: Coding = {
  code: 'level2',
  display: 'Level 2',
  system: 'http://hl7.org/fhir/identity-assuranceLevel',
};
const identity_assuranceLevel_Level3: Coding = {
  code: 'level3',
  display: 'Level 3',
  system: 'http://hl7.org/fhir/identity-assuranceLevel',
};
const identity_assuranceLevel_Level4: Coding = {
  code: 'level4',
  display: 'Level 4',
  system: 'http://hl7.org/fhir/identity-assuranceLevel',
};
/**
 * The level of confidence that this link represents the same actual person, based on NIST Authentication Levels.
 */
export const IdentityAssuranceLevel = {
  /**
   * Little or no confidence in the asserted identity's accuracy.
   */
  Level1: identity_assuranceLevel_Level1,
  /**
   * Some confidence in the asserted identity's accuracy.
   */
  Level2: identity_assuranceLevel_Level2,
  /**
   * High confidence in the asserted identity's accuracy.
   */
  Level3: identity_assuranceLevel_Level3,
  /**
   * Very high confidence in the asserted identity's accuracy.
   */
  Level4: identity_assuranceLevel_Level4,
};
const imagingstudy_status_Available: Coding = {
  code: 'available',
  display: 'Available',
  system: 'http://hl7.org/fhir/imagingstudy-status',
};
const imagingstudy_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/imagingstudy-status',
};
const imagingstudy_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/imagingstudy-status',
};
const imagingstudy_status_Registered: Coding = {
  code: 'registered',
  display: 'Registered',
  system: 'http://hl7.org/fhir/imagingstudy-status',
};
const imagingstudy_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/imagingstudy-status',
};
/**
 * The status of the ImagingStudy.
 */
export const ImagingstudyStatus = {
  /**
   * At least one instance has been associated with this imaging study.
   */
  Available: imagingstudy_status_Available,
  /**
   * The imaging study is unavailable because the imaging study was not started or not completed (also sometimes called "aborted").
   */
  Cancelled: imagingstudy_status_Cancelled,
  /**
   * The imaging study has been withdrawn following a previous final release.  This electronic record should never have existed, though it is possible that real-world decisions were based on it. (If real-world activity has occurred, the status should be "cancelled" rather than "entered-in-error".).
   */
  EnteredInError: imagingstudy_status_EnteredInError,
  /**
   * The existence of the imaging study is registered, but there is nothing yet available.
   */
  Registered: imagingstudy_status_Registered,
  /**
   * The system does not know which of the status values currently applies for this request. Note: This concept is not to be used for "other" - one of the listed statuses is presumed to apply, it's just not known which one.
   */
  Unknown: imagingstudy_status_Unknown,
};
const immunization_evaluation_dose_status_NotValid: Coding = {
  code: 'notvalid',
  display: 'Not valid',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-evaluation-dose-status',
};
const immunization_evaluation_dose_status_Valid: Coding = {
  code: 'valid',
  display: 'Valid',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-evaluation-dose-status',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the validity of a dose relative to a particular recommended schedule. This value set is provided as a suggestive example.
 */
export const ImmunizationEvaluationDoseStatus = {
  /**
   * The dose does not count toward fulfilling a path to immunity for a patient.
   */
  NotValid: immunization_evaluation_dose_status_NotValid,
  /**
   * The dose counts toward fulfilling a path to immunity for a patient, providing protection against the target disease.
   */
  Valid: immunization_evaluation_dose_status_Valid,
};
const immunization_evaluation_dose_status_reason_AdverseStorageCondition: Coding = {
  code: 'advstorage',
  display: 'Adverse storage condition',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-evaluation-dose-status-reason',
};
const immunization_evaluation_dose_status_reason_ColdChainBreak: Coding = {
  code: 'coldchbrk',
  display: 'Cold chain break',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-evaluation-dose-status-reason',
};
const immunization_evaluation_dose_status_reason_ExpiredLot: Coding = {
  code: 'explot',
  display: 'Expired lot',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-evaluation-dose-status-reason',
};
const immunization_evaluation_dose_status_reason_AdministeredOutsideRecommendedSchedule: Coding = {
  code: 'outsidesched',
  display: 'Administered outside recommended schedule',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-evaluation-dose-status-reason',
};
const immunization_evaluation_dose_status_reason_ProductRecall: Coding = {
  code: 'prodrecall',
  display: 'Product recall',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-evaluation-dose-status-reason',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the reason why an administered dose has been assigned a particular status. Often, this reason describes why a dose is considered invalid. This value set is provided as a suggestive example.
 */
export const ImmunizationEvaluationDoseStatusReason = {
  /**
   * The product was stored in a manner inconsistent with manufacturer guidelines potentially reducing the effectiveness of the product.
   */
  AdverseStorageCondition: immunization_evaluation_dose_status_reason_AdverseStorageCondition,
  /**
   * The product was stored at a temperature inconsistent with manufacturer guidelines potentially reducing the effectiveness of the product.
   */
  ColdChainBreak: immunization_evaluation_dose_status_reason_ColdChainBreak,
  /**
   * The product was administered after the expiration date associated with lot of vaccine.
   */
  ExpiredLot: immunization_evaluation_dose_status_reason_ExpiredLot,
  /**
   * The product was administered at a time inconsistent with the documented schedule.
   */
  AdministeredOutsideRecommendedSchedule: immunization_evaluation_dose_status_reason_AdministeredOutsideRecommendedSchedule,
  /**
   * The product administered has been recalled by the manufacturer.
   */
  ProductRecall: immunization_evaluation_dose_status_reason_ProductRecall,
};
const medication_admin_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://terminology.hl7.org/CodeSystem/medication-admin-status',
};
const medication_admin_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://terminology.hl7.org/CodeSystem/medication-admin-status',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the current status of the evaluation for vaccine administration event.
 */
export const ImmunizationEvaluationStatus = {
  /**
   * All actions that are implied by the administration have occurred.
   */
  Completed: medication_admin_status_Completed,
  /**
   * The administration was entered in error and therefore nullified.
   */
  EnteredInError: medication_admin_status_EnteredInError,
};
const ImmunizationEvaluationTargetDisease_VAL14189004: Coding = {
  code: '14189004',
  system: 'http://snomed.info/sct',
};
const ImmunizationEvaluationTargetDisease_VAL1857005: Coding = {
  code: '1857005',
  system: 'http://snomed.info/sct',
};
const ImmunizationEvaluationTargetDisease_VAL27836007: Coding = {
  code: '27836007',
  system: 'http://snomed.info/sct',
};
const ImmunizationEvaluationTargetDisease_VAL36653000: Coding = {
  code: '36653000',
  system: 'http://snomed.info/sct',
};
const ImmunizationEvaluationTargetDisease_VAL36989005: Coding = {
  code: '36989005',
  system: 'http://snomed.info/sct',
};
const ImmunizationEvaluationTargetDisease_VAL397430003: Coding = {
  code: '397430003',
  system: 'http://snomed.info/sct',
};
const ImmunizationEvaluationTargetDisease_VAL398102009: Coding = {
  code: '398102009',
  system: 'http://snomed.info/sct',
};
const ImmunizationEvaluationTargetDisease_VAL709410003: Coding = {
  code: '709410003',
  system: 'http://snomed.info/sct',
};
const ImmunizationEvaluationTargetDisease_VAL76902006: Coding = {
  code: '76902006',
  system: 'http://snomed.info/sct',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the disease that the evaluation is against. This value set is provided as a suggestive example and includes the SNOMED CT concepts from the 64572001 (Disease) hierarchy.
 */
export const ImmunizationEvaluationTargetDisease = {
  VAL14189004: ImmunizationEvaluationTargetDisease_VAL14189004,
  VAL1857005: ImmunizationEvaluationTargetDisease_VAL1857005,
  VAL27836007: ImmunizationEvaluationTargetDisease_VAL27836007,
  VAL36653000: ImmunizationEvaluationTargetDisease_VAL36653000,
  VAL36989005: ImmunizationEvaluationTargetDisease_VAL36989005,
  VAL397430003: ImmunizationEvaluationTargetDisease_VAL397430003,
  VAL398102009: ImmunizationEvaluationTargetDisease_VAL398102009,
  VAL709410003: ImmunizationEvaluationTargetDisease_VAL709410003,
  VAL76902006: ImmunizationEvaluationTargetDisease_VAL76902006,
};
const v2_0443_AdministeringProvider: Coding = {
  code: 'AP',
  display: 'Administering Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_OrderingProvider: Coding = {
  code: 'OP',
  display: 'Ordering Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the function a practitioner or organization may play in the immunization event. This value set is provided as a suggestive example.
 */
export const ImmunizationFunction = {
  AdministeringProvider: v2_0443_AdministeringProvider,
  OrderingProvider: v2_0443_OrderingProvider,
};
const immunization_funding_source_Private: Coding = {
  code: 'private',
  display: 'Private',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-funding-source',
};
const immunization_funding_source_Public: Coding = {
  code: 'public',
  display: 'Public',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-funding-source',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the source of the vaccine administered. This value set is provided as a suggestive example.
 */
export const ImmunizationFundingSource = {
  /**
   * The vaccine was purchased with private funds.
   */
  Private: immunization_funding_source_Private,
  /**
   * The vaccine was purchased with public funds.
   */
  Public: immunization_funding_source_Public,
};
const immunization_origin_OtherProvider: Coding = {
  code: 'provider',
  display: 'Other Provider',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-origin',
};
const immunization_origin_ParentGuardianPatientRecall: Coding = {
  code: 'recall',
  display: 'Parent/Guardian/Patient Recall',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-origin',
};
const immunization_origin_WrittenRecord: Coding = {
  code: 'record',
  display: 'Written Record',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-origin',
};
const immunization_origin_SchoolRecord: Coding = {
  code: 'school',
  display: 'School Record',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-origin',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the source of the data when the report of the immunization event is not based on information from the person, entity or organization who administered the vaccine. This value set is provided as a suggestive example.
 */
export const ImmunizationOrigin = {
  /**
   * The data for the immunization event originated with another provider.
   */
  OtherProvider: immunization_origin_OtherProvider,
  /**
   * The data for the immunization event originated from the recollection of the patient or parent/guardian of the patient.
   */
  ParentGuardianPatientRecall: immunization_origin_ParentGuardianPatientRecall,
  /**
   * The data for the immunization event originated with a written record for the patient.
   */
  WrittenRecord: immunization_origin_WrittenRecord,
  /**
   * The data for the immunization event originated with a school record for the patient.
   */
  SchoolRecord: immunization_origin_SchoolRecord,
};
const immunization_program_eligibility_NotEligible: Coding = {
  code: 'ineligible',
  display: 'Not Eligible',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-program-eligibility',
};
const immunization_program_eligibility_Uninsured: Coding = {
  code: 'uninsured',
  display: 'Uninsured',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-program-eligibility',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the patient's eligibility for a vaccination program. This value set is provided as a suggestive example.
 */
export const ImmunizationProgramEligibility = {
  /**
   * The patient is not eligible for the funding program.
   */
  NotEligible: immunization_program_eligibility_NotEligible,
  /**
   * The patient is eligible for the funding program because they are uninsured.
   */
  Uninsured: immunization_program_eligibility_Uninsured,
};
const ImmunizationReason_VAL281657000: Coding = {
  code: '281657000',
  system: 'http://snomed.info/sct',
};
const ImmunizationReason_VAL429060002: Coding = {
  code: '429060002',
  system: 'http://snomed.info/sct',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the reason why a dose of vaccine was administered. This value set is provided as a suggestive example.
 */
export const ImmunizationReason = {
  VAL281657000: ImmunizationReason_VAL281657000,
  VAL429060002: ImmunizationReason_VAL429060002,
};
const ImmunizationRecommendationDateCriterion_VAL309807: Coding = {
  code: '30980-7',
  system: 'http://loinc.org',
};
const ImmunizationRecommendationDateCriterion_VAL309815: Coding = {
  code: '30981-5',
  system: 'http://loinc.org',
};
const ImmunizationRecommendationDateCriterion_VAL597773: Coding = {
  code: '59777-3',
  system: 'http://loinc.org',
};
const ImmunizationRecommendationDateCriterion_VAL597781: Coding = {
  code: '59778-1',
  system: 'http://loinc.org',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support the definition of dates relevant to recommendations for future doses of vaccines. This value set is provided as a suggestive example.
 */
export const ImmunizationRecommendationDateCriterion = {
  VAL309807: ImmunizationRecommendationDateCriterion_VAL309807,
  VAL309815: ImmunizationRecommendationDateCriterion_VAL309815,
  VAL597773: ImmunizationRecommendationDateCriterion_VAL597773,
  VAL597781: ImmunizationRecommendationDateCriterion_VAL597781,
};
const ImmunizationRecommendationReason_VAL77176002: Coding = {
  code: '77176002',
  system: 'http://snomed.info/sct',
};
const ImmunizationRecommendationReason_VAL77386006: Coding = {
  code: '77386006',
  system: 'http://snomed.info/sct',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the reasons why a given recommendation status is assigned. This value set is provided as a suggestive example and includes SNOMED CT concepts.
 */
export const ImmunizationRecommendationReason = {
  VAL77176002: ImmunizationRecommendationReason_VAL77176002,
  VAL77386006: ImmunizationRecommendationReason_VAL77386006,
};
const immunization_recommendation_status_Complete: Coding = {
  code: 'complete',
  display: 'Complete',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-recommendation-status',
};
const immunization_recommendation_status_Contraindicated: Coding = {
  code: 'contraindicated',
  display: 'Contraindicated',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-recommendation-status',
};
const immunization_recommendation_status_Due: Coding = {
  code: 'due',
  display: 'Due',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-recommendation-status',
};
const immunization_recommendation_status_Immune: Coding = {
  code: 'immune',
  display: 'Immune',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-recommendation-status',
};
const immunization_recommendation_status_Overdue: Coding = {
  code: 'overdue',
  display: 'Overdue',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-recommendation-status',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the status of the patient towards perceived immunity against a vaccine preventable disease. This value set is provided as a suggestive example.
 */
export const ImmunizationRecommendationStatus = {
  /**
   * The patient is fully protected and no further doses are recommended.
   */
  Complete: immunization_recommendation_status_Complete,
  /**
   * The patient is contraindicated for futher doses.
   */
  Contraindicated: immunization_recommendation_status_Contraindicated,
  /**
   * The patient is due for their next vaccination.
   */
  Due: immunization_recommendation_status_Due,
  /**
   * The patient is immune to the target disease and further immunization against the disease is not likely to provide benefit.
   */
  Immune: immunization_recommendation_status_Immune,
  /**
   * The patient is considered overdue for their next vaccination.
   */
  Overdue: immunization_recommendation_status_Overdue,
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the disease targeted by a vaccination recommendation. This value set is provided as a suggestive example and includes the SNOMED CT concepts from the 64572001 (Disease) hierarchy.
 */
export const ImmunizationRecommendationTargetDisease = {
  VAL14189004: ImmunizationEvaluationTargetDisease_VAL14189004,
  VAL1857005: ImmunizationEvaluationTargetDisease_VAL1857005,
  VAL27836007: ImmunizationEvaluationTargetDisease_VAL27836007,
  VAL36653000: ImmunizationEvaluationTargetDisease_VAL36653000,
  VAL36989005: ImmunizationEvaluationTargetDisease_VAL36989005,
  VAL397430003: ImmunizationEvaluationTargetDisease_VAL397430003,
  VAL398102009: ImmunizationEvaluationTargetDisease_VAL398102009,
  VAL709410003: ImmunizationEvaluationTargetDisease_VAL709410003,
  VAL76902006: ImmunizationEvaluationTargetDisease_VAL76902006,
};
const v3_RouteOfAdministration_InjectionIntradermal: Coding = {
  code: 'IDINJ',
  display: 'Injection, intradermal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntramuscular: Coding = {
  code: 'IM',
  display: 'Injection, intramuscular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntravenous: Coding = {
  code: 'IVINJ',
  display: 'Injection, intravenous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InhalationNasalCannula: Coding = {
  code: 'NASINHLC',
  display: 'Inhalation, nasal cannula',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionSubcutaneous: Coding = {
  code: 'SQ',
  display: 'Injection, subcutaneous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Transdermal: Coding = {
  code: 'TRNSDERM',
  display: 'Transdermal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the administrative routes used during vaccination. This value set is provided as a suggestive example.
 */
export const ImmunizationRoute = {
  /**
   * Injection, intradermal
   */
  InjectionIntradermal: v3_RouteOfAdministration_InjectionIntradermal,
  /**
   * Injection, intramuscular
   */
  InjectionIntramuscular: v3_RouteOfAdministration_InjectionIntramuscular,
  /**
   * Injection, intravenous
   */
  InjectionIntravenous: v3_RouteOfAdministration_InjectionIntravenous,
  /**
   * Inhalation, nasal, prongs
   */
  InhalationNasalCannula: v3_RouteOfAdministration_InhalationNasalCannula,
  /**
   * Swallow, oral
   */
  SwallowOral: v3_RouteOfAdministration_SwallowOral,
  /**
   * Injection, subcutaneous
   */
  InjectionSubcutaneous: v3_RouteOfAdministration_InjectionSubcutaneous,
  /**
   * Transdermal
   */
  Transdermal: v3_RouteOfAdministration_Transdermal,
};
const v3_ActSite_LeftArm: Coding = {
  code: 'LA',
  display: 'left arm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightArm: Coding = {
  code: 'RA',
  display: 'right arm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the body site where the vaccination occurred. This value set is provided as a suggestive example.
 */
export const ImmunizationSite = {
  /**
   * left arm
   */
  LeftArm: v3_ActSite_LeftArm,
  /**
   * right arm
   */
  RightArm: v3_ActSite_RightArm,
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the current status of the administered dose of vaccine.
 */
export const ImmunizationStatus = {
  /**
   * The event has now concluded.
   */
  Completed: event_status_Completed,
  /**
   * This electronic record should never have existed, though it is possible that real-world decisions were based on it.  (If real-world activity has occurred, the status should be "stopped" rather than "entered-in-error".).
   */
  EnteredInError: event_status_EnteredInError,
  /**
   * The event was terminated prior to any activity beyond preparation.  I.e. The 'main' activity has not yet begun.  The boundary between preparatory and the 'main' activity is context-specific.
   */
  NotDone: event_status_NotDone,
};
const v3_ActReason_Immunity: Coding = {
  code: 'IMMUNE',
  display: 'immunity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_MedicalPrecaution: Coding = {
  code: 'MEDPREC',
  display: 'medical precaution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ProductOutOfStock: Coding = {
  code: 'OSTOCK',
  display: 'product out of stock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientObjection: Coding = {
  code: 'PATOBJ',
  display: 'patient objection',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the reason why a dose of vaccine was not administered. This value set is provided as a suggestive example.
 */
export const ImmunizationStatusReason = {
  /**
   * Definition:Testing has shown that the patient already has immunity to the agent targeted by the immunization.
   */
  Immunity_v3_ActReason: v3_ActReason_Immunity,
  /**
   * Definition:The patient currently has a medical condition for which the vaccine is contraindicated or for which precaution is warranted.
   */
  MedicalPrecaution_v3_ActReason: v3_ActReason_MedicalPrecaution,
  /**
   * Definition:There was no supply of the product on hand to perform the service.
   */
  ProductOutOfStock_v3_ActReason: v3_ActReason_ProductOutOfStock,
  /**
   * Definition:The patient or their guardian objects to receiving the vaccine.
   */
  PatientObjection_v3_ActReason: v3_ActReason_PatientObjection,
};
const immunization_subpotent_reason_ColdChainBreak: Coding = {
  code: 'coldchainbreak',
  display: 'Cold Chain Break',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-subpotent-reason',
};
const immunization_subpotent_reason_PartialDose: Coding = {
  code: 'partial',
  display: 'Partial Dose',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-subpotent-reason',
};
const immunization_subpotent_reason_ManufacturerRecall: Coding = {
  code: 'recall',
  display: 'Manufacturer Recall',
  system: 'http://terminology.hl7.org/CodeSystem/immunization-subpotent-reason',
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the reason why a dose is considered to be subpotent. This value set is provided as a suggestive example.
 */
export const ImmunizationSubpotentReason = {
  /**
   * The vaccine experienced a cold chain break.
   */
  ColdChainBreak: immunization_subpotent_reason_ColdChainBreak,
  /**
   * The full volume of the dose was not administered to the patient.
   */
  PartialDose: immunization_subpotent_reason_PartialDose,
  /**
   * The vaccine was recalled by the manufacturer.
   */
  ManufacturerRecall: immunization_subpotent_reason_ManufacturerRecall,
};
/**
 * The value set to instantiate this attribute should be drawn from a terminologically robust code system that consists of or contains concepts to support describing the disease that the dose is being administered against. This value set is provided as a suggestive example and includes the SNOMED CT concepts from the 64572001 (Disease) hierarchy.
 */
export const ImmunizationTargetDisease = {
  VAL14189004: ImmunizationEvaluationTargetDisease_VAL14189004,
  VAL1857005: ImmunizationEvaluationTargetDisease_VAL1857005,
  VAL27836007: ImmunizationEvaluationTargetDisease_VAL27836007,
  VAL36653000: ImmunizationEvaluationTargetDisease_VAL36653000,
  VAL36989005: ImmunizationEvaluationTargetDisease_VAL36989005,
  VAL397430003: ImmunizationEvaluationTargetDisease_VAL397430003,
  VAL398102009: ImmunizationEvaluationTargetDisease_VAL398102009,
  VAL709410003: ImmunizationEvaluationTargetDisease_VAL709410003,
  VAL76902006: ImmunizationEvaluationTargetDisease_VAL76902006,
};
const implantStatus_Disabled: Coding = {
  code: 'disabled',
  display: 'Disabled',
  system: 'http://terminology.hl7.org/CodeSystem/implantStatus',
};
const implantStatus_Functional: Coding = {
  code: 'functional',
  display: 'Functional',
  system: 'http://terminology.hl7.org/CodeSystem/implantStatus',
};
const implantStatus_NonFunctional: Coding = {
  code: 'non-functional',
  display: 'Non-Functional',
  system: 'http://terminology.hl7.org/CodeSystem/implantStatus',
};
const implantStatus_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/implantStatus',
};
/**
 * A set codes that define the functional status of an implanted device.
 */
export const ImplantStatus = {
  /**
   * The implanted device has been turned off.
   */
  Disabled: implantStatus_Disabled,
  /**
   * The implanted device is working normally.
   */
  Functional: implantStatus_Functional,
  /**
   * The implanted device is not working.
   */
  NonFunctional: implantStatus_NonFunctional,
  /**
   * the functional status of the implant has not been determined.
   */
  Unknown: implantStatus_Unknown,
};
const v3_ActMood_Criterion: Coding = {
  code: 'CRT',
  display: 'criterion',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Expectation: Coding = {
  code: 'EXPEC',
  display: 'expectation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Goal: Coding = {
  code: 'GOL',
  display: 'Goal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Option: Coding = {
  code: 'OPT',
  display: 'option',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Risk: Coding = {
  code: 'RSK',
  display: 'risk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
/**
 * HL7 v3 ActMood Predicate codes, including inactive codes
 */
export const Inactive = {
  Criterion: v3_ActMood_Criterion,
  /**
   * Definition: An act that is considered to have some noteworthy likelihood of occurring in the future (has_match = event).
   *
   *
   *                            Examples:Prognosis of a condition, Expected date of discharge from hospital, patient will likely need an emergency decompression of the intracranial pressure by morning.
   *
   *
   *                            UsageNotes:INT (intent) reflects a plan for the future, which is a declaration to do something. This contrasts with expectation, which is a prediction that something will happen in the future. GOL (goal) reflects a hope rather than a prediction. RSK (risk) reflects a potential negative event that may or may not be expected to happen.
   */
  Expectation: v3_ActMood_Expectation,
  /**
   * Definition: An expectation that is considered to be desirable to occur in the future
   *
   *
   *                            Examples:Target weight below 80Kg, Stop smoking, Regain ability to walk, goal is to administer thrombolytics to candidate patients presenting with acute myocardial infarction.
   *
   *
   *                            UsageNotes: INT (intent) reflects a plan for the future, which is a declaration to do something.  This contrasts with goal which doesn't represent an intention to act, merely a hope for an eventual result.  A goal is distinct from the intended actions to reach that goal.  "I will reduce the dose of drug x to 20mg" is an intent.  "I hope to be able to get the patient to the point where I can reduce the dose of drug x to 20mg" is a goal. EXPEC (expectation) reflects a prediction rather than a hope. RSK (risk) reflects a potential negative event rather than a hope.
   */
  Goal: v3_ActMood_Goal,
  /**
   * Definition: One of a set of acts that specify an option for the property values that the parent act may have. Typically used in definitions or orders to describe alternatives. An option can only be used as a group, that is, all assigned values must be used together. The actual mood of the act is the same as the parent act, and they must be linked by an actrelationship with type = OPTN.
   */
  Option: v3_ActMood_Option,
  /**
   * Definition:An act that may occur in the future and which is regarded as undesirable
   *
   *
   *                            Examples:Increased risk of DVT, at risk for sub-acute bacterial endocarditis.
   *
   *
   *                            UsageNotes:Note: An observation in RSK mood expresses the undesirable act, and not the underlying risk factor. A risk factor that is present (e.g. obesity, smoking, etc) should be expressed in event mood. INT (intent) reflects a plan for the future, which is a declaration to do something. This contrasts with RSK (risk), which is the potential that something negative will occur that may or may not ever happen. GOL (goal) reflects a hope to achieve something. EXPEC (expectation) is the prediction of a positive or negative event. This contrasts with RSK (risk), which is the potential that something negative will occur that may or may not ever happen, and may not be expected to happen.
   */
  Risk: v3_ActMood_Risk,
};
const dicom_dcim_Nearline: Coding = {
  code: 'NEARLINE',
  display: 'Nearline',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_Offline: Coding = {
  code: 'OFFLINE',
  display: 'Offline',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_Online: Coding = {
  code: 'ONLINE',
  display: 'Online',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_Unavailable: Coding = {
  code: 'UNAVAILABLE',
  display: 'Unavailable',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
/**
 * Availability of the resource.
 */
export const InstanceAvailability = {
  /**
   * Instances need to be retrieved from relatively slow media such as optical disk or tape
   */
  Nearline: dicom_dcim_Nearline,
  /**
   * Instances need to be retrieved by manual intervention
   */
  Offline: dicom_dcim_Offline,
  /**
   * Instances are immediately available
   */
  Online: dicom_dcim_Online,
  /**
   * Instances cannot be retrieved
   */
  Unavailable: dicom_dcim_Unavailable,
};
const insuranceplan_applicability_InNetwork: Coding = {
  code: 'in-network',
  display: 'In Network',
  system: 'http://terminology.hl7.org/CodeSystem/applicability',
};
const insuranceplan_applicability_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/applicability',
};
const insuranceplan_applicability_OutOfNetwork: Coding = {
  code: 'out-of-network',
  display: 'Out of Network',
  system: 'http://terminology.hl7.org/CodeSystem/applicability',
};
/**
 * Whether the cost applies to in-network or out-of-network providers.
 */
export const InsuranceplanApplicability = {
  /**
   * Provider is contracted with the health insurance company to provide services to plan members for specific pre-negotiated rates
   */
  InNetwork: insuranceplan_applicability_InNetwork,
  /**
   * Other applicability
   */
  Other: insuranceplan_applicability_Other,
  /**
   * Provider is  not contracted with the health insurance company to provide services to plan members for specific pre-negotiated rates
   */
  OutOfNetwork: insuranceplan_applicability_OutOfNetwork,
};
const insuranceplan_type_Dental: Coding = {
  code: 'dental',
  display: 'Dental',
  system: 'http://terminology.hl7.org/CodeSystem/insurance-plan-type',
};
const insuranceplan_type_Drug: Coding = {
  code: 'Drug',
  display: 'Drug',
  system: 'http://terminology.hl7.org/CodeSystem/insurance-plan-type',
};
const insuranceplan_type_HomeHealth: Coding = {
  code: 'home',
  display: 'Home Health',
  system: 'http://terminology.hl7.org/CodeSystem/insurance-plan-type',
};
const insuranceplan_type_Hospice: Coding = {
  code: 'hospice',
  display: 'Hospice',
  system: 'http://terminology.hl7.org/CodeSystem/insurance-plan-type',
};
const insuranceplan_type_LongTermCare: Coding = {
  code: 'long-term',
  display: 'Long Term Care',
  system: 'http://terminology.hl7.org/CodeSystem/insurance-plan-type',
};
const insuranceplan_type_Medical: Coding = {
  code: 'medical',
  display: 'Medical',
  system: 'http://terminology.hl7.org/CodeSystem/insurance-plan-type',
};
const insuranceplan_type_MentalHealth: Coding = {
  code: 'mental',
  display: 'Mental Health',
  system: 'http://terminology.hl7.org/CodeSystem/insurance-plan-type',
};
const insuranceplan_type_ShortTerm: Coding = {
  code: 'short-term',
  display: 'Short Term',
  system: 'http://terminology.hl7.org/CodeSystem/insurance-plan-type',
};
const insuranceplan_type_SubstanceAbuse: Coding = {
  code: 'subst-ab',
  display: 'Substance Abuse',
  system: 'http://terminology.hl7.org/CodeSystem/insurance-plan-type',
};
const insuranceplan_type_Vision: Coding = {
  code: 'vision',
  display: 'Vision',
  system: 'http://terminology.hl7.org/CodeSystem/insurance-plan-type',
};
/**
 * This example value set defines a set of codes that can be used to indicate a type of insurance plan.
 */
export const InsuranceplanType = {
  Dental: insuranceplan_type_Dental,
  Drug: insuranceplan_type_Drug,
  HomeHealth: insuranceplan_type_HomeHealth,
  Hospice: insuranceplan_type_Hospice,
  LongTermCare: insuranceplan_type_LongTermCare,
  Medical: insuranceplan_type_Medical,
  MentalHealth: insuranceplan_type_MentalHealth,
  ShortTerm: insuranceplan_type_ShortTerm,
  SubstanceAbuse: insuranceplan_type_SubstanceAbuse,
  Vision: insuranceplan_type_Vision,
};
const intervention_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://hl7.org/fhir/intervention',
};
const intervention_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/intervention',
};
/**
 * This value set includes sample Intervention codes.
 */
export const Intervention = {
  /**
   * Other
   */
  Other: intervention_Other,
  /**
   * Unknown
   */
  Unknown: intervention_Unknown,
};
const InvestigationSets_HistorySymptoms: Coding = {
  code: '160237006',
  display: 'History/symptoms',
  system: 'http://snomed.info/sct',
};
const InvestigationSets_ExaminationSigns: Coding = {
  code: '271336007',
  display: 'Examination / signs',
  system: 'http://snomed.info/sct',
};
/**
 * Example value set for investigation type.
 */
export const InvestigationSets = {
  HistorySymptoms: InvestigationSets_HistorySymptoms,
  ExaminationSigns: InvestigationSets_ExaminationSigns,
};
const invoice_priceComponentType_BasePrice: Coding = {
  code: 'base',
  display: 'base price',
  system: 'http://hl7.org/fhir/invoice-priceComponentType',
};
const invoice_priceComponentType_Deduction: Coding = {
  code: 'deduction',
  display: 'deduction',
  system: 'http://hl7.org/fhir/invoice-priceComponentType',
};
const invoice_priceComponentType_Discount: Coding = {
  code: 'discount',
  display: 'discount',
  system: 'http://hl7.org/fhir/invoice-priceComponentType',
};
const invoice_priceComponentType_Informational: Coding = {
  code: 'informational',
  display: 'informational',
  system: 'http://hl7.org/fhir/invoice-priceComponentType',
};
const invoice_priceComponentType_Surcharge: Coding = {
  code: 'surcharge',
  display: 'surcharge',
  system: 'http://hl7.org/fhir/invoice-priceComponentType',
};
const invoice_priceComponentType_Tax: Coding = {
  code: 'tax',
  display: 'tax',
  system: 'http://hl7.org/fhir/invoice-priceComponentType',
};
/**
 * Codes indicating the kind of the price component.
 */
export const InvoicePriceComponentType = {
  /**
   * the amount is the base price used for calculating the total price before applying surcharges, discount or taxes.
   */
  BasePrice: invoice_priceComponentType_BasePrice,
  /**
   * the amount is a deduction applied on the base price.
   */
  Deduction: invoice_priceComponentType_Deduction,
  /**
   * the amount is a discount applied on the base price.
   */
  Discount: invoice_priceComponentType_Discount,
  /**
   * the amount is of informational character, it has not been applied in the calculation of the total price.
   */
  Informational: invoice_priceComponentType_Informational,
  /**
   * the amount is a surcharge applied on the base price.
   */
  Surcharge: invoice_priceComponentType_Surcharge,
  /**
   * the amount is the tax component of the total price.
   */
  Tax: invoice_priceComponentType_Tax,
};
const invoice_status_Balanced: Coding = {
  code: 'balanced',
  display: 'balanced',
  system: 'http://hl7.org/fhir/invoice-status',
};
const invoice_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'cancelled',
  system: 'http://hl7.org/fhir/invoice-status',
};
const invoice_status_Draft: Coding = {
  code: 'draft',
  display: 'draft',
  system: 'http://hl7.org/fhir/invoice-status',
};
const invoice_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'entered in error',
  system: 'http://hl7.org/fhir/invoice-status',
};
const invoice_status_Issued: Coding = {
  code: 'issued',
  display: 'issued',
  system: 'http://hl7.org/fhir/invoice-status',
};
/**
 * Codes identifying the lifecycle stage of an Invoice.
 */
export const InvoiceStatus = {
  /**
   * the invoice has been balaced / completely paid.
   */
  Balanced: invoice_status_Balanced,
  /**
   * the invoice was cancelled.
   */
  Cancelled: invoice_status_Cancelled,
  /**
   * the invoice has been prepared but not yet finalized.
   */
  Draft: invoice_status_Draft,
  /**
   * the invoice was determined as entered in error before it was issued.
   */
  EnteredInError: invoice_status_EnteredInError,
  /**
   * the invoice has been finalized and sent to the recipient.
   */
  Issued: invoice_status_Issued,
};
const Iso316612_Andorra: Coding = {
  code: 'AD',
  display: 'Andorra',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_UnitedArabEmirates: Coding = {
  code: 'AE',
  display: 'United Arab Emirates',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Afghanistan: Coding = {
  code: 'AF',
  display: 'Afghanistan',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_AntiguaAndBarbuda: Coding = {
  code: 'AG',
  display: 'Antigua and Barbuda',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Anguilla: Coding = {
  code: 'AI',
  display: 'Anguilla',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Albania: Coding = {
  code: 'AL',
  display: 'Albania',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Armenia: Coding = {
  code: 'AM',
  display: 'Armenia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Angola: Coding = {
  code: 'AO',
  display: 'Angola',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Antarctica: Coding = {
  code: 'AQ',
  display: 'Antarctica',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Argentina: Coding = {
  code: 'AR',
  display: 'Argentina',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_AmericanSamoa: Coding = {
  code: 'AS',
  display: 'American Samoa',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Austria: Coding = {
  code: 'AT',
  display: 'Austria',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Australia: Coding = {
  code: 'AU',
  display: 'Australia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Aruba: Coding = {
  code: 'AW',
  display: 'Aruba',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_LandIslands: Coding = {
  code: 'AX',
  display: 'Åland Islands',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Azerbaijan: Coding = {
  code: 'AZ',
  display: 'Azerbaijan',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_BosniaAndHerzegovina: Coding = {
  code: 'BA',
  display: 'Bosnia and Herzegovina',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Barbados: Coding = {
  code: 'BB',
  display: 'Barbados',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Bangladesh: Coding = {
  code: 'BD',
  display: 'Bangladesh',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Belgium: Coding = {
  code: 'BE',
  display: 'Belgium',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_BurkinaFaso: Coding = {
  code: 'BF',
  display: 'Burkina Faso',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Bulgaria: Coding = {
  code: 'BG',
  display: 'Bulgaria',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Bahrain: Coding = {
  code: 'BH',
  display: 'Bahrain',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Burundi: Coding = {
  code: 'BI',
  display: 'Burundi',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Benin: Coding = {
  code: 'BJ',
  display: 'Benin',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SaintBarthlemy: Coding = {
  code: 'BL',
  display: 'Saint Barthélemy',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Bermuda: Coding = {
  code: 'BM',
  display: 'Bermuda',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_BruneiDarussalam: Coding = {
  code: 'BN',
  display: 'Brunei Darussalam',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_BoliviaPlurinationalStateOf: Coding = {
  code: 'BO',
  display: 'Bolivia, Plurinational State of',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_BonaireSintEustatiusAndSaba: Coding = {
  code: 'BQ',
  display: 'Bonaire, Sint Eustatius and Saba',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Brazil: Coding = {
  code: 'BR',
  display: 'Brazil',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Bahamas: Coding = {
  code: 'BS',
  display: 'Bahamas',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Bhutan: Coding = {
  code: 'BT',
  display: 'Bhutan',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_BouvetIsland: Coding = {
  code: 'BV',
  display: 'Bouvet Island',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Botswana: Coding = {
  code: 'BW',
  display: 'Botswana',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Belarus: Coding = {
  code: 'BY',
  display: 'Belarus',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Belize: Coding = {
  code: 'BZ',
  display: 'Belize',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Canada: Coding = {
  code: 'CA',
  display: 'Canada',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_CocosKeelingIslands: Coding = {
  code: 'CC',
  display: 'Cocos (Keeling) Islands',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_CongoTheDemocraticRepublicOfThe: Coding = {
  code: 'CD',
  display: 'Congo, the Democratic Republic of the',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_CentralAfricanRepublic: Coding = {
  code: 'CF',
  display: 'Central African Republic',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Congo: Coding = {
  code: 'CG',
  display: 'Congo',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Switzerland: Coding = {
  code: 'CH',
  display: 'Switzerland',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_CteDDoubleQuoteIvoire: Coding = {
  code: 'CI',
  display: "Côte d''Ivoire",
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_CookIslands: Coding = {
  code: 'CK',
  display: 'Cook Islands',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Chile: Coding = {
  code: 'CL',
  display: 'Chile',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Cameroon: Coding = {
  code: 'CM',
  display: 'Cameroon',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_China: Coding = {
  code: 'CN',
  display: 'China',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Colombia: Coding = {
  code: 'CO',
  display: 'Colombia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_CostaRica: Coding = {
  code: 'CR',
  display: 'Costa Rica',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Cuba: Coding = {
  code: 'CU',
  display: 'Cuba',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_CaboVerde: Coding = {
  code: 'CV',
  display: 'Cabo Verde',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Curaao: Coding = {
  code: 'CW',
  display: 'Curaçao',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_ChristmasIsland: Coding = {
  code: 'CX',
  display: 'Christmas Island',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Cyprus: Coding = {
  code: 'CY',
  display: 'Cyprus',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Czechia: Coding = {
  code: 'CZ',
  display: 'Czechia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Germany: Coding = {
  code: 'DE',
  display: 'Germany',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Djibouti: Coding = {
  code: 'DJ',
  display: 'Djibouti',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Denmark: Coding = {
  code: 'DK',
  display: 'Denmark',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Dominica: Coding = {
  code: 'DM',
  display: 'Dominica',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_DominicanRepublic: Coding = {
  code: 'DO',
  display: 'Dominican Republic',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Algeria: Coding = {
  code: 'DZ',
  display: 'Algeria',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Ecuador: Coding = {
  code: 'EC',
  display: 'Ecuador',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Estonia: Coding = {
  code: 'EE',
  display: 'Estonia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Egypt: Coding = {
  code: 'EG',
  display: 'Egypt',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_WesternSahara: Coding = {
  code: 'EH',
  display: 'Western Sahara',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Eritrea: Coding = {
  code: 'ER',
  display: 'Eritrea',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Spain: Coding = {
  code: 'ES',
  display: 'Spain',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Ethiopia: Coding = {
  code: 'ET',
  display: 'Ethiopia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Finland: Coding = {
  code: 'FI',
  display: 'Finland',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Fiji: Coding = {
  code: 'FJ',
  display: 'Fiji',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_FalklandIslandsMalvinas: Coding = {
  code: 'FK',
  display: 'Falkland Islands (Malvinas)',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_MicronesiaFederatedStatesOf: Coding = {
  code: 'FM',
  display: 'Micronesia, Federated States of',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_FaroeIslands: Coding = {
  code: 'FO',
  display: 'Faroe Islands',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_France: Coding = {
  code: 'FR',
  display: 'France',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Gabon: Coding = {
  code: 'GA',
  display: 'Gabon',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_UnitedKingdomOfGreatBritainAndNorthernIreland: Coding = {
  code: 'GB',
  display: 'United Kingdom of Great Britain and Northern Ireland',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Grenada: Coding = {
  code: 'GD',
  display: 'Grenada',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Georgia: Coding = {
  code: 'GE',
  display: 'Georgia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_FrenchGuiana: Coding = {
  code: 'GF',
  display: 'French Guiana',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Guernsey: Coding = {
  code: 'GG',
  display: 'Guernsey',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Ghana: Coding = {
  code: 'GH',
  display: 'Ghana',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Gibraltar: Coding = {
  code: 'GI',
  display: 'Gibraltar',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Greenland: Coding = {
  code: 'GL',
  display: 'Greenland',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Gambia: Coding = {
  code: 'GM',
  display: 'Gambia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Guinea: Coding = {
  code: 'GN',
  display: 'Guinea',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Guadeloupe: Coding = {
  code: 'GP',
  display: 'Guadeloupe',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_EquatorialGuinea: Coding = {
  code: 'GQ',
  display: 'Equatorial Guinea',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Greece: Coding = {
  code: 'GR',
  display: 'Greece',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SouthGeorgiaAndTheSouthSandwichIslands: Coding = {
  code: 'GS',
  display: 'South Georgia and the South Sandwich Islands',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Guatemala: Coding = {
  code: 'GT',
  display: 'Guatemala',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Guam: Coding = {
  code: 'GU',
  display: 'Guam',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_GuineaBissau: Coding = {
  code: 'GW',
  display: 'Guinea-Bissau',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Guyana: Coding = {
  code: 'GY',
  display: 'Guyana',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_HongKong: Coding = {
  code: 'HK',
  display: 'Hong Kong',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_HeardIslandAndMcDonaldIslands: Coding = {
  code: 'HM',
  display: 'Heard Island and McDonald Islands',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Honduras: Coding = {
  code: 'HN',
  display: 'Honduras',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Croatia: Coding = {
  code: 'HR',
  display: 'Croatia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Haiti: Coding = {
  code: 'HT',
  display: 'Haiti',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Hungary: Coding = {
  code: 'HU',
  display: 'Hungary',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Indonesia: Coding = {
  code: 'ID',
  display: 'Indonesia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Ireland: Coding = {
  code: 'IE',
  display: 'Ireland',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Israel: Coding = {
  code: 'IL',
  display: 'Israel',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_IsleOfMan: Coding = {
  code: 'IM',
  display: 'Isle of Man',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_India: Coding = {
  code: 'IN',
  display: 'India',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_BritishIndianOceanTerritory: Coding = {
  code: 'IO',
  display: 'British Indian Ocean Territory',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Iraq: Coding = {
  code: 'IQ',
  display: 'Iraq',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_IranIslamicRepublicOf: Coding = {
  code: 'IR',
  display: 'Iran, Islamic Republic of',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Iceland: Coding = {
  code: 'IS',
  display: 'Iceland',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Italy: Coding = {
  code: 'IT',
  display: 'Italy',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Jersey: Coding = {
  code: 'JE',
  display: 'Jersey',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Jamaica: Coding = {
  code: 'JM',
  display: 'Jamaica',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Jordan: Coding = {
  code: 'JO',
  display: 'Jordan',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Japan: Coding = {
  code: 'JP',
  display: 'Japan',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Kenya: Coding = {
  code: 'KE',
  display: 'Kenya',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Kyrgyzstan: Coding = {
  code: 'KG',
  display: 'Kyrgyzstan',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Cambodia: Coding = {
  code: 'KH',
  display: 'Cambodia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Kiribati: Coding = {
  code: 'KI',
  display: 'Kiribati',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Comoros: Coding = {
  code: 'KM',
  display: 'Comoros',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SaintKittsAndNevis: Coding = {
  code: 'KN',
  display: 'Saint Kitts and Nevis',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_KoreaDemocraticPeopleDoubleQuoteSRepublicOf: Coding = {
  code: 'KP',
  display: "Korea, Democratic People''s Republic of",
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_KoreaRepublicOf: Coding = {
  code: 'KR',
  display: 'Korea, Republic of',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Kuwait: Coding = {
  code: 'KW',
  display: 'Kuwait',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_CaymanIslands: Coding = {
  code: 'KY',
  display: 'Cayman Islands',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Kazakhstan: Coding = {
  code: 'KZ',
  display: 'Kazakhstan',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_LaoPeopleDoubleQuoteSDemocraticRepublic: Coding = {
  code: 'LA',
  display: "Lao People''s Democratic Republic",
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Lebanon: Coding = {
  code: 'LB',
  display: 'Lebanon',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SaintLucia: Coding = {
  code: 'LC',
  display: 'Saint Lucia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Liechtenstein: Coding = {
  code: 'LI',
  display: 'Liechtenstein',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SriLanka: Coding = {
  code: 'LK',
  display: 'Sri Lanka',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Liberia: Coding = {
  code: 'LR',
  display: 'Liberia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Lesotho: Coding = {
  code: 'LS',
  display: 'Lesotho',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Lithuania: Coding = {
  code: 'LT',
  display: 'Lithuania',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Luxembourg: Coding = {
  code: 'LU',
  display: 'Luxembourg',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Latvia: Coding = {
  code: 'LV',
  display: 'Latvia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Libya: Coding = {
  code: 'LY',
  display: 'Libya',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Morocco: Coding = {
  code: 'MA',
  display: 'Morocco',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Monaco: Coding = {
  code: 'MC',
  display: 'Monaco',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_MoldovaRepublicOf: Coding = {
  code: 'MD',
  display: 'Moldova, Republic of',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Montenegro: Coding = {
  code: 'ME',
  display: 'Montenegro',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SaintMartinFrenchPart: Coding = {
  code: 'MF',
  display: 'Saint Martin (French part)',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Madagascar: Coding = {
  code: 'MG',
  display: 'Madagascar',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_MarshallIslands: Coding = {
  code: 'MH',
  display: 'Marshall Islands',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_MacedoniaTheFormerYugoslavRepublicOf: Coding = {
  code: 'MK',
  display: 'Macedonia, the former Yugoslav Republic of',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Mali: Coding = {
  code: 'ML',
  display: 'Mali',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Myanmar: Coding = {
  code: 'MM',
  display: 'Myanmar',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Mongolia: Coding = {
  code: 'MN',
  display: 'Mongolia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Macao: Coding = {
  code: 'MO',
  display: 'Macao',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_NorthernMarianaIslands: Coding = {
  code: 'MP',
  display: 'Northern Mariana Islands',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Martinique: Coding = {
  code: 'MQ',
  display: 'Martinique',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Mauritania: Coding = {
  code: 'MR',
  display: 'Mauritania',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Montserrat: Coding = {
  code: 'MS',
  display: 'Montserrat',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Malta: Coding = {
  code: 'MT',
  display: 'Malta',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Mauritius: Coding = {
  code: 'MU',
  display: 'Mauritius',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Maldives: Coding = {
  code: 'MV',
  display: 'Maldives',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Malawi: Coding = {
  code: 'MW',
  display: 'Malawi',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Mexico: Coding = {
  code: 'MX',
  display: 'Mexico',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Malaysia: Coding = {
  code: 'MY',
  display: 'Malaysia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Mozambique: Coding = {
  code: 'MZ',
  display: 'Mozambique',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Namibia: Coding = {
  code: 'NA',
  display: 'Namibia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_NewCaledonia: Coding = {
  code: 'NC',
  display: 'New Caledonia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Niger: Coding = {
  code: 'NE',
  display: 'Niger',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_NorfolkIsland: Coding = {
  code: 'NF',
  display: 'Norfolk Island',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Nigeria: Coding = {
  code: 'NG',
  display: 'Nigeria',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Nicaragua: Coding = {
  code: 'NI',
  display: 'Nicaragua',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Netherlands: Coding = {
  code: 'NL',
  display: 'Netherlands',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Norway: Coding = {
  code: 'NO',
  display: 'Norway',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Nepal: Coding = {
  code: 'NP',
  display: 'Nepal',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Nauru: Coding = {
  code: 'NR',
  display: 'Nauru',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Niue: Coding = {
  code: 'NU',
  display: 'Niue',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_NewZealand: Coding = {
  code: 'NZ',
  display: 'New Zealand',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Oman: Coding = {
  code: 'OM',
  display: 'Oman',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Panama: Coding = {
  code: 'PA',
  display: 'Panama',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Peru: Coding = {
  code: 'PE',
  display: 'Peru',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_FrenchPolynesia: Coding = {
  code: 'PF',
  display: 'French Polynesia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_PapuaNewGuinea: Coding = {
  code: 'PG',
  display: 'Papua New Guinea',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Philippines: Coding = {
  code: 'PH',
  display: 'Philippines',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Pakistan: Coding = {
  code: 'PK',
  display: 'Pakistan',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Poland: Coding = {
  code: 'PL',
  display: 'Poland',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SaintPierreAndMiquelon: Coding = {
  code: 'PM',
  display: 'Saint Pierre and Miquelon',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Pitcairn: Coding = {
  code: 'PN',
  display: 'Pitcairn',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_PuertoRico: Coding = {
  code: 'PR',
  display: 'Puerto Rico',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_PalestineStateOf: Coding = {
  code: 'PS',
  display: 'Palestine, State of',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Portugal: Coding = {
  code: 'PT',
  display: 'Portugal',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Palau: Coding = {
  code: 'PW',
  display: 'Palau',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Paraguay: Coding = {
  code: 'PY',
  display: 'Paraguay',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Qatar: Coding = {
  code: 'QA',
  display: 'Qatar',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Runion: Coding = {
  code: 'RE',
  display: 'Réunion',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Romania: Coding = {
  code: 'RO',
  display: 'Romania',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Serbia: Coding = {
  code: 'RS',
  display: 'Serbia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_RussianFederation: Coding = {
  code: 'RU',
  display: 'Russian Federation',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Rwanda: Coding = {
  code: 'RW',
  display: 'Rwanda',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SaudiArabia: Coding = {
  code: 'SA',
  display: 'Saudi Arabia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SolomonIslands: Coding = {
  code: 'SB',
  display: 'Solomon Islands',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Seychelles: Coding = {
  code: 'SC',
  display: 'Seychelles',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Sudan: Coding = {
  code: 'SD',
  display: 'Sudan',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Sweden: Coding = {
  code: 'SE',
  display: 'Sweden',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Singapore: Coding = {
  code: 'SG',
  display: 'Singapore',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SaintHelenaAscensionAndTristanDaCunha: Coding = {
  code: 'SH',
  display: 'Saint Helena, Ascension and Tristan da Cunha',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Slovenia: Coding = {
  code: 'SI',
  display: 'Slovenia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SvalbardAndJanMayen: Coding = {
  code: 'SJ',
  display: 'Svalbard and Jan Mayen',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Slovakia: Coding = {
  code: 'SK',
  display: 'Slovakia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SierraLeone: Coding = {
  code: 'SL',
  display: 'Sierra Leone',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SanMarino: Coding = {
  code: 'SM',
  display: 'San Marino',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Senegal: Coding = {
  code: 'SN',
  display: 'Senegal',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Somalia: Coding = {
  code: 'SO',
  display: 'Somalia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Suriname: Coding = {
  code: 'SR',
  display: 'Suriname',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SouthSudan: Coding = {
  code: 'SS',
  display: 'South Sudan',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SaoTomeAndPrincipe: Coding = {
  code: 'ST',
  display: 'Sao Tome and Principe',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_ElSalvador: Coding = {
  code: 'SV',
  display: 'El Salvador',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SintMaartenDutchPart: Coding = {
  code: 'SX',
  display: 'Sint Maarten (Dutch part)',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SyrianArabRepublic: Coding = {
  code: 'SY',
  display: 'Syrian Arab Republic',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Swaziland: Coding = {
  code: 'SZ',
  display: 'Swaziland',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_TurksAndCaicosIslands: Coding = {
  code: 'TC',
  display: 'Turks and Caicos Islands',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Chad: Coding = {
  code: 'TD',
  display: 'Chad',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_FrenchSouthernTerritories: Coding = {
  code: 'TF',
  display: 'French Southern Territories',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Togo: Coding = {
  code: 'TG',
  display: 'Togo',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Thailand: Coding = {
  code: 'TH',
  display: 'Thailand',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Tajikistan: Coding = {
  code: 'TJ',
  display: 'Tajikistan',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Tokelau: Coding = {
  code: 'TK',
  display: 'Tokelau',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_TimorLeste: Coding = {
  code: 'TL',
  display: 'Timor-Leste',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Turkmenistan: Coding = {
  code: 'TM',
  display: 'Turkmenistan',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Tunisia: Coding = {
  code: 'TN',
  display: 'Tunisia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Tonga: Coding = {
  code: 'TO',
  display: 'Tonga',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Turkey: Coding = {
  code: 'TR',
  display: 'Turkey',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_TrinidadAndTobago: Coding = {
  code: 'TT',
  display: 'Trinidad and Tobago',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Tuvalu: Coding = {
  code: 'TV',
  display: 'Tuvalu',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_TaiwanProvinceOfChina: Coding = {
  code: 'TW',
  display: 'Taiwan, Province of China',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_TanzaniaUnitedRepublicOf: Coding = {
  code: 'TZ',
  display: 'Tanzania, United Republic of',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Ukraine: Coding = {
  code: 'UA',
  display: 'Ukraine',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Uganda: Coding = {
  code: 'UG',
  display: 'Uganda',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_UnitedStatesMinorOutlyingIslands: Coding = {
  code: 'UM',
  display: 'United States Minor Outlying Islands',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_UnitedStatesOfAmerica: Coding = {
  code: 'US',
  display: 'United States of America',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Uruguay: Coding = {
  code: 'UY',
  display: 'Uruguay',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Uzbekistan: Coding = {
  code: 'UZ',
  display: 'Uzbekistan',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_HolySee: Coding = {
  code: 'VA',
  display: 'Holy See',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SaintVincentAndTheGrenadines: Coding = {
  code: 'VC',
  display: 'Saint Vincent and the Grenadines',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_VenezuelaBolivarianRepublicOf: Coding = {
  code: 'VE',
  display: 'Venezuela, Bolivarian Republic of',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_VirginIslandsBritish: Coding = {
  code: 'VG',
  display: 'Virgin Islands, British',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_VirginIslands: Coding = {
  code: 'VI',
  display: 'Virgin Islands,',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_VietNam: Coding = {
  code: 'VN',
  display: 'Viet Nam',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Vanuatu: Coding = {
  code: 'VU',
  display: 'Vanuatu',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_WallisAndFutuna: Coding = {
  code: 'WF',
  display: 'Wallis and Futuna',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Samoa: Coding = {
  code: 'WS',
  display: 'Samoa',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Yemen: Coding = {
  code: 'YE',
  display: 'Yemen',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Mayotte: Coding = {
  code: 'YT',
  display: 'Mayotte',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_SouthAfrica: Coding = {
  code: 'ZA',
  display: 'South Africa',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Zambia: Coding = {
  code: 'ZM',
  display: 'Zambia',
  system: 'urn:iso:std:iso:3166',
};
const Iso316612_Zimbabwe: Coding = {
  code: 'ZW',
  display: 'Zimbabwe',
  system: 'urn:iso:std:iso:3166',
};
/**
 * This value set defines the ISO 3166 Part 1 2-letter codes
 */
export const Iso316612 = {
  Andorra: Iso316612_Andorra,
  UnitedArabEmirates: Iso316612_UnitedArabEmirates,
  Afghanistan: Iso316612_Afghanistan,
  AntiguaAndBarbuda: Iso316612_AntiguaAndBarbuda,
  Anguilla: Iso316612_Anguilla,
  Albania: Iso316612_Albania,
  Armenia: Iso316612_Armenia,
  Angola: Iso316612_Angola,
  Antarctica: Iso316612_Antarctica,
  Argentina: Iso316612_Argentina,
  AmericanSamoa: Iso316612_AmericanSamoa,
  Austria: Iso316612_Austria,
  Australia: Iso316612_Australia,
  Aruba: Iso316612_Aruba,
  LandIslands: Iso316612_LandIslands,
  Azerbaijan: Iso316612_Azerbaijan,
  BosniaAndHerzegovina: Iso316612_BosniaAndHerzegovina,
  Barbados: Iso316612_Barbados,
  Bangladesh: Iso316612_Bangladesh,
  Belgium: Iso316612_Belgium,
  BurkinaFaso: Iso316612_BurkinaFaso,
  Bulgaria: Iso316612_Bulgaria,
  Bahrain: Iso316612_Bahrain,
  Burundi: Iso316612_Burundi,
  Benin: Iso316612_Benin,
  SaintBarthlemy: Iso316612_SaintBarthlemy,
  Bermuda: Iso316612_Bermuda,
  BruneiDarussalam: Iso316612_BruneiDarussalam,
  BoliviaPlurinationalStateOf: Iso316612_BoliviaPlurinationalStateOf,
  BonaireSintEustatiusAndSaba: Iso316612_BonaireSintEustatiusAndSaba,
  Brazil: Iso316612_Brazil,
  Bahamas: Iso316612_Bahamas,
  Bhutan: Iso316612_Bhutan,
  BouvetIsland: Iso316612_BouvetIsland,
  Botswana: Iso316612_Botswana,
  Belarus: Iso316612_Belarus,
  Belize: Iso316612_Belize,
  Canada: Iso316612_Canada,
  CocosKeelingIslands: Iso316612_CocosKeelingIslands,
  CongoTheDemocraticRepublicOfThe: Iso316612_CongoTheDemocraticRepublicOfThe,
  CentralAfricanRepublic: Iso316612_CentralAfricanRepublic,
  Congo: Iso316612_Congo,
  Switzerland: Iso316612_Switzerland,
  CteDDoubleQuoteIvoire: Iso316612_CteDDoubleQuoteIvoire,
  CookIslands: Iso316612_CookIslands,
  Chile: Iso316612_Chile,
  Cameroon: Iso316612_Cameroon,
  China: Iso316612_China,
  Colombia: Iso316612_Colombia,
  CostaRica: Iso316612_CostaRica,
  Cuba: Iso316612_Cuba,
  CaboVerde: Iso316612_CaboVerde,
  Curaao: Iso316612_Curaao,
  ChristmasIsland: Iso316612_ChristmasIsland,
  Cyprus: Iso316612_Cyprus,
  Czechia: Iso316612_Czechia,
  Germany: Iso316612_Germany,
  Djibouti: Iso316612_Djibouti,
  Denmark: Iso316612_Denmark,
  Dominica: Iso316612_Dominica,
  DominicanRepublic: Iso316612_DominicanRepublic,
  Algeria: Iso316612_Algeria,
  Ecuador: Iso316612_Ecuador,
  Estonia: Iso316612_Estonia,
  Egypt: Iso316612_Egypt,
  WesternSahara: Iso316612_WesternSahara,
  Eritrea: Iso316612_Eritrea,
  Spain: Iso316612_Spain,
  Ethiopia: Iso316612_Ethiopia,
  Finland: Iso316612_Finland,
  Fiji: Iso316612_Fiji,
  FalklandIslandsMalvinas: Iso316612_FalklandIslandsMalvinas,
  MicronesiaFederatedStatesOf: Iso316612_MicronesiaFederatedStatesOf,
  FaroeIslands: Iso316612_FaroeIslands,
  France: Iso316612_France,
  Gabon: Iso316612_Gabon,
  UnitedKingdomOfGreatBritainAndNorthernIreland: Iso316612_UnitedKingdomOfGreatBritainAndNorthernIreland,
  Grenada: Iso316612_Grenada,
  Georgia: Iso316612_Georgia,
  FrenchGuiana: Iso316612_FrenchGuiana,
  Guernsey: Iso316612_Guernsey,
  Ghana: Iso316612_Ghana,
  Gibraltar: Iso316612_Gibraltar,
  Greenland: Iso316612_Greenland,
  Gambia: Iso316612_Gambia,
  Guinea: Iso316612_Guinea,
  Guadeloupe: Iso316612_Guadeloupe,
  EquatorialGuinea: Iso316612_EquatorialGuinea,
  Greece: Iso316612_Greece,
  SouthGeorgiaAndTheSouthSandwichIslands: Iso316612_SouthGeorgiaAndTheSouthSandwichIslands,
  Guatemala: Iso316612_Guatemala,
  Guam: Iso316612_Guam,
  GuineaBissau: Iso316612_GuineaBissau,
  Guyana: Iso316612_Guyana,
  HongKong: Iso316612_HongKong,
  HeardIslandAndMcDonaldIslands: Iso316612_HeardIslandAndMcDonaldIslands,
  Honduras: Iso316612_Honduras,
  Croatia: Iso316612_Croatia,
  Haiti: Iso316612_Haiti,
  Hungary: Iso316612_Hungary,
  Indonesia: Iso316612_Indonesia,
  Ireland: Iso316612_Ireland,
  Israel: Iso316612_Israel,
  IsleOfMan: Iso316612_IsleOfMan,
  India: Iso316612_India,
  BritishIndianOceanTerritory: Iso316612_BritishIndianOceanTerritory,
  Iraq: Iso316612_Iraq,
  IranIslamicRepublicOf: Iso316612_IranIslamicRepublicOf,
  Iceland: Iso316612_Iceland,
  Italy: Iso316612_Italy,
  Jersey: Iso316612_Jersey,
  Jamaica: Iso316612_Jamaica,
  Jordan: Iso316612_Jordan,
  Japan: Iso316612_Japan,
  Kenya: Iso316612_Kenya,
  Kyrgyzstan: Iso316612_Kyrgyzstan,
  Cambodia: Iso316612_Cambodia,
  Kiribati: Iso316612_Kiribati,
  Comoros: Iso316612_Comoros,
  SaintKittsAndNevis: Iso316612_SaintKittsAndNevis,
  KoreaDemocraticPeopleDoubleQuoteSRepublicOf: Iso316612_KoreaDemocraticPeopleDoubleQuoteSRepublicOf,
  KoreaRepublicOf: Iso316612_KoreaRepublicOf,
  Kuwait: Iso316612_Kuwait,
  CaymanIslands: Iso316612_CaymanIslands,
  Kazakhstan: Iso316612_Kazakhstan,
  LaoPeopleDoubleQuoteSDemocraticRepublic: Iso316612_LaoPeopleDoubleQuoteSDemocraticRepublic,
  Lebanon: Iso316612_Lebanon,
  SaintLucia: Iso316612_SaintLucia,
  Liechtenstein: Iso316612_Liechtenstein,
  SriLanka: Iso316612_SriLanka,
  Liberia: Iso316612_Liberia,
  Lesotho: Iso316612_Lesotho,
  Lithuania: Iso316612_Lithuania,
  Luxembourg: Iso316612_Luxembourg,
  Latvia: Iso316612_Latvia,
  Libya: Iso316612_Libya,
  Morocco: Iso316612_Morocco,
  Monaco: Iso316612_Monaco,
  MoldovaRepublicOf: Iso316612_MoldovaRepublicOf,
  Montenegro: Iso316612_Montenegro,
  SaintMartinFrenchPart: Iso316612_SaintMartinFrenchPart,
  Madagascar: Iso316612_Madagascar,
  MarshallIslands: Iso316612_MarshallIslands,
  MacedoniaTheFormerYugoslavRepublicOf: Iso316612_MacedoniaTheFormerYugoslavRepublicOf,
  Mali: Iso316612_Mali,
  Myanmar: Iso316612_Myanmar,
  Mongolia: Iso316612_Mongolia,
  Macao: Iso316612_Macao,
  NorthernMarianaIslands: Iso316612_NorthernMarianaIslands,
  Martinique: Iso316612_Martinique,
  Mauritania: Iso316612_Mauritania,
  Montserrat: Iso316612_Montserrat,
  Malta: Iso316612_Malta,
  Mauritius: Iso316612_Mauritius,
  Maldives: Iso316612_Maldives,
  Malawi: Iso316612_Malawi,
  Mexico: Iso316612_Mexico,
  Malaysia: Iso316612_Malaysia,
  Mozambique: Iso316612_Mozambique,
  Namibia: Iso316612_Namibia,
  NewCaledonia: Iso316612_NewCaledonia,
  Niger: Iso316612_Niger,
  NorfolkIsland: Iso316612_NorfolkIsland,
  Nigeria: Iso316612_Nigeria,
  Nicaragua: Iso316612_Nicaragua,
  Netherlands: Iso316612_Netherlands,
  Norway: Iso316612_Norway,
  Nepal: Iso316612_Nepal,
  Nauru: Iso316612_Nauru,
  Niue: Iso316612_Niue,
  NewZealand: Iso316612_NewZealand,
  Oman: Iso316612_Oman,
  Panama: Iso316612_Panama,
  Peru: Iso316612_Peru,
  FrenchPolynesia: Iso316612_FrenchPolynesia,
  PapuaNewGuinea: Iso316612_PapuaNewGuinea,
  Philippines: Iso316612_Philippines,
  Pakistan: Iso316612_Pakistan,
  Poland: Iso316612_Poland,
  SaintPierreAndMiquelon: Iso316612_SaintPierreAndMiquelon,
  Pitcairn: Iso316612_Pitcairn,
  PuertoRico: Iso316612_PuertoRico,
  PalestineStateOf: Iso316612_PalestineStateOf,
  Portugal: Iso316612_Portugal,
  Palau: Iso316612_Palau,
  Paraguay: Iso316612_Paraguay,
  Qatar: Iso316612_Qatar,
  Runion: Iso316612_Runion,
  Romania: Iso316612_Romania,
  Serbia: Iso316612_Serbia,
  RussianFederation: Iso316612_RussianFederation,
  Rwanda: Iso316612_Rwanda,
  SaudiArabia: Iso316612_SaudiArabia,
  SolomonIslands: Iso316612_SolomonIslands,
  Seychelles: Iso316612_Seychelles,
  Sudan: Iso316612_Sudan,
  Sweden: Iso316612_Sweden,
  Singapore: Iso316612_Singapore,
  SaintHelenaAscensionAndTristanDaCunha: Iso316612_SaintHelenaAscensionAndTristanDaCunha,
  Slovenia: Iso316612_Slovenia,
  SvalbardAndJanMayen: Iso316612_SvalbardAndJanMayen,
  Slovakia: Iso316612_Slovakia,
  SierraLeone: Iso316612_SierraLeone,
  SanMarino: Iso316612_SanMarino,
  Senegal: Iso316612_Senegal,
  Somalia: Iso316612_Somalia,
  Suriname: Iso316612_Suriname,
  SouthSudan: Iso316612_SouthSudan,
  SaoTomeAndPrincipe: Iso316612_SaoTomeAndPrincipe,
  ElSalvador: Iso316612_ElSalvador,
  SintMaartenDutchPart: Iso316612_SintMaartenDutchPart,
  SyrianArabRepublic: Iso316612_SyrianArabRepublic,
  Swaziland: Iso316612_Swaziland,
  TurksAndCaicosIslands: Iso316612_TurksAndCaicosIslands,
  Chad: Iso316612_Chad,
  FrenchSouthernTerritories: Iso316612_FrenchSouthernTerritories,
  Togo: Iso316612_Togo,
  Thailand: Iso316612_Thailand,
  Tajikistan: Iso316612_Tajikistan,
  Tokelau: Iso316612_Tokelau,
  TimorLeste: Iso316612_TimorLeste,
  Turkmenistan: Iso316612_Turkmenistan,
  Tunisia: Iso316612_Tunisia,
  Tonga: Iso316612_Tonga,
  Turkey: Iso316612_Turkey,
  TrinidadAndTobago: Iso316612_TrinidadAndTobago,
  Tuvalu: Iso316612_Tuvalu,
  TaiwanProvinceOfChina: Iso316612_TaiwanProvinceOfChina,
  TanzaniaUnitedRepublicOf: Iso316612_TanzaniaUnitedRepublicOf,
  Ukraine: Iso316612_Ukraine,
  Uganda: Iso316612_Uganda,
  UnitedStatesMinorOutlyingIslands: Iso316612_UnitedStatesMinorOutlyingIslands,
  UnitedStatesOfAmerica: Iso316612_UnitedStatesOfAmerica,
  Uruguay: Iso316612_Uruguay,
  Uzbekistan: Iso316612_Uzbekistan,
  HolySee: Iso316612_HolySee,
  SaintVincentAndTheGrenadines: Iso316612_SaintVincentAndTheGrenadines,
  VenezuelaBolivarianRepublicOf: Iso316612_VenezuelaBolivarianRepublicOf,
  VirginIslandsBritish: Iso316612_VirginIslandsBritish,
  VirginIslands: Iso316612_VirginIslands,
  VietNam: Iso316612_VietNam,
  Vanuatu: Iso316612_Vanuatu,
  WallisAndFutuna: Iso316612_WallisAndFutuna,
  Samoa: Iso316612_Samoa,
  Yemen: Iso316612_Yemen,
  Mayotte: Iso316612_Mayotte,
  SouthAfrica: Iso316612_SouthAfrica,
  Zambia: Iso316612_Zambia,
  Zimbabwe: Iso316612_Zimbabwe,
};
const issue_severity_Error: Coding = {
  code: 'error',
  display: 'Error',
  system: 'http://hl7.org/fhir/issue-severity',
};
const issue_severity_Fatal: Coding = {
  code: 'fatal',
  display: 'Fatal',
  system: 'http://hl7.org/fhir/issue-severity',
};
const issue_severity_Information: Coding = {
  code: 'information',
  display: 'Information',
  system: 'http://hl7.org/fhir/issue-severity',
};
const issue_severity_Warning: Coding = {
  code: 'warning',
  display: 'Warning',
  system: 'http://hl7.org/fhir/issue-severity',
};
/**
 * How the issue affects the success of the action.
 */
export const IssueSeverity = {
  /**
   * The issue is sufficiently important to cause the action to fail.
   */
  Error: issue_severity_Error,
  /**
   * The issue caused the action to fail and no further checking could be performed.
   */
  Fatal: issue_severity_Fatal,
  /**
   * The issue has no relation to the degree of success of the action.
   */
  Information: issue_severity_Information,
  /**
   * The issue is not important enough to cause the action to fail but may cause it to be performed suboptimally or in a way that is not as desired.
   */
  Warning: issue_severity_Warning,
};
const issue_type_BusinessRuleViolation: Coding = {
  code: 'business-rule',
  display: 'Business Rule Violation',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_InvalidCode: Coding = {
  code: 'code-invalid',
  display: 'Invalid Code',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_EditVersionConflict: Coding = {
  code: 'conflict',
  display: 'Edit Version Conflict',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_Deleted: Coding = {
  code: 'deleted',
  display: 'Deleted',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_Duplicate: Coding = {
  code: 'duplicate',
  display: 'Duplicate',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_Exception: Coding = {
  code: 'exception',
  display: 'Exception',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_SessionExpired: Coding = {
  code: 'expired',
  display: 'Session Expired',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_UnacceptableExtension: Coding = {
  code: 'extension',
  display: 'Unacceptable Extension',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_Forbidden: Coding = {
  code: 'forbidden',
  display: 'Forbidden',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_IncompleteResults: Coding = {
  code: 'incomplete',
  display: 'Incomplete Results',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_InformationalNote: Coding = {
  code: 'informational',
  display: 'Informational Note',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_InvalidContent: Coding = {
  code: 'invalid',
  display: 'Invalid Content',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_ValidationRuleFailed: Coding = {
  code: 'invariant',
  display: 'Validation rule failed',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_LockError: Coding = {
  code: 'lock-error',
  display: 'Lock Error',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_LoginRequired: Coding = {
  code: 'login',
  display: 'Login Required',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_MultipleMatches: Coding = {
  code: 'multiple-matches',
  display: 'Multiple Matches',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_NoStoreAvailable: Coding = {
  code: 'no-store',
  display: 'No Store Available',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_NotFound: Coding = {
  code: 'not-found',
  display: 'Not Found',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_ContentNotSupported: Coding = {
  code: 'not-supported',
  display: 'Content not supported',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_ProcessingFailure: Coding = {
  code: 'processing',
  display: 'Processing Failure',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_RequiredElementMissing: Coding = {
  code: 'required',
  display: 'Required element missing',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_SecurityProblem: Coding = {
  code: 'security',
  display: 'Security Problem',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_StructuralIssue: Coding = {
  code: 'structure',
  display: 'Structural Issue',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_InformationSuppressed: Coding = {
  code: 'suppressed',
  display: 'Information  Suppressed',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_Throttled: Coding = {
  code: 'throttled',
  display: 'Throttled',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_Timeout: Coding = {
  code: 'timeout',
  display: 'Timeout',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_OperationTooCostly: Coding = {
  code: 'too-costly',
  display: 'Operation Too Costly',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_ContentTooLong: Coding = {
  code: 'too-long',
  display: 'Content Too Long',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_TransientIssue: Coding = {
  code: 'transient',
  display: 'Transient Issue',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_UnknownUser: Coding = {
  code: 'unknown',
  display: 'Unknown User',
  system: 'http://hl7.org/fhir/issue-type',
};
const issue_type_ElementValueInvalid: Coding = {
  code: 'value',
  display: 'Element value invalid',
  system: 'http://hl7.org/fhir/issue-type',
};
/**
 * A code that describes the type of issue.
 */
export const IssueType = {
  /**
   * The content/operation failed to pass some business rule and so could not proceed.
   */
  BusinessRuleViolation: issue_type_BusinessRuleViolation,
  /**
   * The code or system could not be understood, or it was not valid in the context of a particular ValueSet.code.
   */
  InvalidCode: issue_type_InvalidCode,
  /**
   * Content could not be accepted because of an edit conflict (i.e. version aware updates). (In a pure RESTful environment, this would be an HTTP 409 error, but this code may be used where the conflict is discovered further into the application architecture.).
   */
  EditVersionConflict: issue_type_EditVersionConflict,
  /**
   * The reference pointed to content (usually a resource) that has been deleted.
   */
  Deleted: issue_type_Deleted,
  /**
   * An attempt was made to create a duplicate record.
   */
  Duplicate: issue_type_Duplicate,
  /**
   * An unexpected internal error has occurred.
   */
  Exception: issue_type_Exception,
  /**
   * User session expired; a login may be required.
   */
  SessionExpired: issue_type_SessionExpired,
  /**
   * An extension was found that was not acceptable, could not be resolved, or a modifierExtension was not recognized.
   */
  UnacceptableExtension: issue_type_UnacceptableExtension,
  /**
   * The user does not have the rights to perform this action.
   */
  Forbidden: issue_type_Forbidden,
  /**
   * Not all data sources typically accessed could be reached or responded in time, so the returned information might not be complete (applies to search interactions and some operations).
   */
  IncompleteResults: issue_type_IncompleteResults,
  /**
   * A message unrelated to the processing success of the completed operation (examples of the latter include things like reminders of password expiry, system maintenance times, etc.).
   */
  InformationalNote: issue_type_InformationalNote,
  /**
   * Content invalid against the specification or a profile.
   */
  InvalidContent: issue_type_InvalidContent,
  /**
   * A content validation rule failed - e.g. a schematron rule.
   */
  ValidationRuleFailed: issue_type_ValidationRuleFailed,
  /**
   * A resource/record locking failure (usually in an underlying database).
   */
  LockError: issue_type_LockError,
  /**
   * The client needs to initiate an authentication process.
   */
  LoginRequired: issue_type_LoginRequired,
  /**
   * Multiple matching records were found when the operation required only one match.
   */
  MultipleMatches: issue_type_MultipleMatches,
  /**
   * The persistent store is unavailable; e.g. the database is down for maintenance or similar action, and the interaction or operation cannot be processed.
   */
  NoStoreAvailable: issue_type_NoStoreAvailable,
  /**
   * The reference provided was not found. In a pure RESTful environment, this would be an HTTP 404 error, but this code may be used where the content is not found further into the application architecture.
   */
  NotFound: issue_type_NotFound,
  /**
   * The interaction, operation, resource or profile is not supported.
   */
  ContentNotSupported: issue_type_ContentNotSupported,
  /**
   * Processing issues. These are expected to be final e.g. there is no point resubmitting the same content unchanged.
   */
  ProcessingFailure: issue_type_ProcessingFailure,
  /**
   * A required element is missing.
   */
  RequiredElementMissing: issue_type_RequiredElementMissing,
  /**
   * An authentication/authorization/permissions issue of some kind.
   */
  SecurityProblem: issue_type_SecurityProblem,
  /**
   * A structural issue in the content such as wrong namespace, unable to parse the content completely, invalid syntax, etc.
   */
  StructuralIssue: issue_type_StructuralIssue,
  /**
   * Some information was not or might not have been returned due to business rules, consent or privacy rules, or access permission constraints.  This information may be accessible through alternate processes.
   */
  InformationSuppressed: issue_type_InformationSuppressed,
  /**
   * The system is not prepared to handle this request due to load management.
   */
  Throttled: issue_type_Throttled,
  /**
   * An internal timeout has occurred.
   */
  Timeout: issue_type_Timeout,
  /**
   * The operation was stopped to protect server resources; e.g. a request for a value set expansion on all of SNOMED CT.
   */
  OperationTooCostly: issue_type_OperationTooCostly,
  /**
   * Provided content is too long (typically, this is a denial of service protection type of error).
   */
  ContentTooLong: issue_type_ContentTooLong,
  /**
   * Transient processing issues. The system receiving the message may be able to resubmit the same content once an underlying issue is resolved.
   */
  TransientIssue: issue_type_TransientIssue,
  /**
   * The user or system was not able to be authenticated (either there is no process, or the proferred token is unacceptable).
   */
  UnknownUser: issue_type_UnknownUser,
  /**
   * An element or header value is invalid.
   */
  ElementValueInvalid: issue_type_ElementValueInvalid,
};
const item_type_Attachment: Coding = {
  code: 'attachment',
  display: 'Attachment',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_Boolean: Coding = {
  code: 'boolean',
  display: 'Boolean',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_Choice: Coding = {
  code: 'choice',
  display: 'Choice',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_Date: Coding = {
  code: 'date',
  display: 'Date',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_DateTime: Coding = {
  code: 'dateTime',
  display: 'Date Time',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_Decimal: Coding = {
  code: 'decimal',
  display: 'Decimal',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_Display: Coding = {
  code: 'display',
  display: 'Display',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_Group: Coding = {
  code: 'group',
  display: 'Group',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_Integer: Coding = {
  code: 'integer',
  display: 'Integer',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_OpenChoice: Coding = {
  code: 'open-choice',
  display: 'Open Choice',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_Quantity: Coding = {
  code: 'quantity',
  display: 'Quantity',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_Question: Coding = {
  code: 'question',
  display: 'Question',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_Reference: Coding = {
  code: 'reference',
  display: 'Reference',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_String: Coding = {
  code: 'string',
  display: 'String',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_Text: Coding = {
  code: 'text',
  display: 'Text',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_Time: Coding = {
  code: 'time',
  display: 'Time',
  system: 'http://hl7.org/fhir/item-type',
};
const item_type_Url: Coding = {
  code: 'url',
  display: 'Url',
  system: 'http://hl7.org/fhir/item-type',
};
/**
 * Distinguishes groups from questions and display text and indicates data type for questions.
 */
export const ItemType = {
  /**
   * Question with binary content such as an image, PDF, etc. as an answer (valueAttachment).
   */
  Attachment: item_type_Attachment,
  /**
   * Question with a yes/no answer (valueBoolean).
   */
  Boolean: item_type_Boolean,
  /**
   * Question with a Coding drawn from a list of possible answers (specified in either the answerOption property, or via the valueset referenced in the answerValueSet property) as an answer (valueCoding).
   */
  Choice: item_type_Choice,
  /**
   * Question with a date answer (valueDate).
   */
  Date: item_type_Date,
  /**
   * Question with a date and time answer (valueDateTime).
   */
  DateTime: item_type_DateTime,
  /**
   * Question with is a real number answer (valueDecimal).
   */
  Decimal: item_type_Decimal,
  /**
   * Text for display that will not capture an answer or have child items.
   */
  Display: item_type_Display,
  /**
   * An item with no direct answer but should have at least one child item.
   */
  Group: item_type_Group,
  /**
   * Question with an integer answer (valueInteger).
   */
  Integer: item_type_Integer,
  /**
   * Answer is a Coding drawn from a list of possible answers (as with the choice type) or a free-text entry in a string (valueCoding or valueString).
   */
  OpenChoice: item_type_OpenChoice,
  /**
   * Question with a combination of a numeric value and unit, potentially with a comparator (<, >, etc.) as an answer. (valueQuantity) There is an extension 'http://hl7.org/fhir/StructureDefinition/questionnaire-unit' that can be used to define what unit should be captured (or the unit that has a ucum conversion from the provided unit).
   */
  Quantity: item_type_Quantity,
  /**
   * An item that defines a specific answer to be captured, and which may have child items. (the answer provided in the QuestionnaireResponse should be of the defined datatype).
   */
  Question: item_type_Question,
  /**
   * Question with a reference to another resource (practitioner, organization, etc.) as an answer (valueReference).
   */
  Reference: item_type_Reference,
  /**
   * Question with a short (few words to short sentence) free-text entry answer (valueString).
   */
  String: item_type_String,
  /**
   * Question with a long (potentially multi-paragraph) free-text entry answer (valueString).
   */
  Text: item_type_Text,
  /**
   * Question with a time (hour:minute:second) answer independent of date. (valueTime).
   */
  Time: item_type_Time,
  /**
   * Question with a URL (website, FTP site, etc.) answer (valueUri).
   */
  Url: item_type_Url,
};
const knowledge_resource_types_ActivityDefinition: Coding = {
  code: 'ActivityDefinition',
  display: 'ActivityDefinition',
  system: 'http://hl7.org/fhir/knowledge-resource-types',
};
const knowledge_resource_types_CodeSystem: Coding = {
  code: 'CodeSystem',
  display: 'CodeSystem',
  system: 'http://hl7.org/fhir/knowledge-resource-types',
};
const knowledge_resource_types_ConceptMap: Coding = {
  code: 'ConceptMap',
  display: 'ConceptMap',
  system: 'http://hl7.org/fhir/knowledge-resource-types',
};
const knowledge_resource_types_Library: Coding = {
  code: 'Library',
  display: 'Library',
  system: 'http://hl7.org/fhir/knowledge-resource-types',
};
const knowledge_resource_types_Measure: Coding = {
  code: 'Measure',
  display: 'Measure',
  system: 'http://hl7.org/fhir/knowledge-resource-types',
};
const knowledge_resource_types_PlanDefinition: Coding = {
  code: 'PlanDefinition',
  display: 'PlanDefinition',
  system: 'http://hl7.org/fhir/knowledge-resource-types',
};
const knowledge_resource_types_StructureDefinition: Coding = {
  code: 'StructureDefinition',
  display: 'StructureDefinition',
  system: 'http://hl7.org/fhir/knowledge-resource-types',
};
const knowledge_resource_types_StructureMap: Coding = {
  code: 'StructureMap',
  display: 'StructureMap',
  system: 'http://hl7.org/fhir/knowledge-resource-types',
};
const knowledge_resource_types_ValueSet: Coding = {
  code: 'ValueSet',
  display: 'ValueSet',
  system: 'http://hl7.org/fhir/knowledge-resource-types',
};
/**
 * A list of all the knowledge resource types defined in this version of the FHIR specification.
 */
export const KnowledgeResourceTypes = {
  /**
   * The definition of a specific activity to be taken, independent of any particular patient or context.
   */
  ActivityDefinition: knowledge_resource_types_ActivityDefinition,
  /**
   * A set of codes drawn from one or more code systems.
   */
  CodeSystem: knowledge_resource_types_CodeSystem,
  /**
   * A map from one set of concepts to one or more other concepts.
   */
  ConceptMap: knowledge_resource_types_ConceptMap,
  /**
   * Represents a library of quality improvement components.
   */
  Library: knowledge_resource_types_Library,
  /**
   * A quality measure definition.
   */
  Measure: knowledge_resource_types_Measure,
  /**
   * The definition of a plan for a series of actions, independent of any specific patient or context.
   */
  PlanDefinition: knowledge_resource_types_PlanDefinition,
  /**
   * Structural Definition.
   */
  StructureDefinition: knowledge_resource_types_StructureDefinition,
  /**
   * A Map of relationships between 2 structures that can be used to transform data.
   */
  StructureMap: knowledge_resource_types_StructureMap,
  /**
   * A set of codes drawn from one or more code systems.
   */
  ValueSet: knowledge_resource_types_ValueSet,
};
const language_preference_type_Verbal: Coding = {
  code: 'verbal',
  display: 'verbal',
  system: 'http://hl7.org/fhir/language-preference-type',
};
const language_preference_type_Written: Coding = {
  code: 'written',
  display: 'written',
  system: 'http://hl7.org/fhir/language-preference-type',
};
/**
 * This value set defines the set of codes for describing the type or mode of the patient's preferred language.
 */
export const LanguagePreferenceType = {
  /**
   * The patient prefers to verbally communicate with the associated language.
   */
  Verbal: language_preference_type_Verbal,
  /**
   * The patient prefers to communicate in writing with the associated language.
   */
  Written: language_preference_type_Written,
};
const Languages_Arabic: Coding = {
  code: 'ar',
  display: 'Arabic',
  system: 'urn:ietf:bcp:47',
};
const Languages_Bengali: Coding = {
  code: 'bn',
  display: 'Bengali',
  system: 'urn:ietf:bcp:47',
};
const Languages_Czech: Coding = {
  code: 'cs',
  display: 'Czech',
  system: 'urn:ietf:bcp:47',
};
const Languages_Danish: Coding = {
  code: 'da',
  display: 'Danish',
  system: 'urn:ietf:bcp:47',
};
const Languages_German: Coding = {
  code: 'de',
  display: 'German',
  system: 'urn:ietf:bcp:47',
};
const Languages_GermanAustria: Coding = {
  code: 'de-AT',
  display: 'German (Austria)',
  system: 'urn:ietf:bcp:47',
};
const Languages_GermanSwitzerland: Coding = {
  code: 'de-CH',
  display: 'German (Switzerland)',
  system: 'urn:ietf:bcp:47',
};
const Languages_GermanGermany: Coding = {
  code: 'de-DE',
  display: 'German (Germany)',
  system: 'urn:ietf:bcp:47',
};
const Languages_Greek: Coding = {
  code: 'el',
  display: 'Greek',
  system: 'urn:ietf:bcp:47',
};
const Languages_English: Coding = {
  code: 'en',
  display: 'English',
  system: 'urn:ietf:bcp:47',
};
const Languages_EnglishAustralia: Coding = {
  code: 'en-AU',
  display: 'English (Australia)',
  system: 'urn:ietf:bcp:47',
};
const Languages_EnglishCanada: Coding = {
  code: 'en-CA',
  display: 'English (Canada)',
  system: 'urn:ietf:bcp:47',
};
const Languages_EnglishGreatBritain: Coding = {
  code: 'en-GB',
  display: 'English (Great Britain)',
  system: 'urn:ietf:bcp:47',
};
const Languages_EnglishIndia: Coding = {
  code: 'en-IN',
  display: 'English (India)',
  system: 'urn:ietf:bcp:47',
};
const Languages_EnglishNewZeland: Coding = {
  code: 'en-NZ',
  display: 'English (New Zeland)',
  system: 'urn:ietf:bcp:47',
};
const Languages_EnglishSingapore: Coding = {
  code: 'en-SG',
  display: 'English (Singapore)',
  system: 'urn:ietf:bcp:47',
};
const Languages_EnglishUnitedStates: Coding = {
  code: 'en-US',
  display: 'English (United States)',
  system: 'urn:ietf:bcp:47',
};
const Languages_Spanish: Coding = {
  code: 'es',
  display: 'Spanish',
  system: 'urn:ietf:bcp:47',
};
const Languages_SpanishArgentina: Coding = {
  code: 'es-AR',
  display: 'Spanish (Argentina)',
  system: 'urn:ietf:bcp:47',
};
const Languages_SpanishSpain: Coding = {
  code: 'es-ES',
  display: 'Spanish (Spain)',
  system: 'urn:ietf:bcp:47',
};
const Languages_SpanishUruguay: Coding = {
  code: 'es-UY',
  display: 'Spanish (Uruguay)',
  system: 'urn:ietf:bcp:47',
};
const Languages_Finnish: Coding = {
  code: 'fi',
  display: 'Finnish',
  system: 'urn:ietf:bcp:47',
};
const Languages_French: Coding = {
  code: 'fr',
  display: 'French',
  system: 'urn:ietf:bcp:47',
};
const Languages_FrenchBelgium: Coding = {
  code: 'fr-BE',
  display: 'French (Belgium)',
  system: 'urn:ietf:bcp:47',
};
const Languages_FrenchSwitzerland: Coding = {
  code: 'fr-CH',
  display: 'French (Switzerland)',
  system: 'urn:ietf:bcp:47',
};
const Languages_FrenchFrance: Coding = {
  code: 'fr-FR',
  display: 'French (France)',
  system: 'urn:ietf:bcp:47',
};
const Languages_Frysian: Coding = {
  code: 'fy',
  display: 'Frysian',
  system: 'urn:ietf:bcp:47',
};
const Languages_FrysianNetherlands: Coding = {
  code: 'fy-NL',
  display: 'Frysian (Netherlands)',
  system: 'urn:ietf:bcp:47',
};
const Languages_Hindi: Coding = {
  code: 'hi',
  display: 'Hindi',
  system: 'urn:ietf:bcp:47',
};
const Languages_Croatian: Coding = {
  code: 'hr',
  display: 'Croatian',
  system: 'urn:ietf:bcp:47',
};
const Languages_Italian: Coding = {
  code: 'it',
  display: 'Italian',
  system: 'urn:ietf:bcp:47',
};
const Languages_ItalianSwitzerland: Coding = {
  code: 'it-CH',
  display: 'Italian (Switzerland)',
  system: 'urn:ietf:bcp:47',
};
const Languages_ItalianItaly: Coding = {
  code: 'it-IT',
  display: 'Italian (Italy)',
  system: 'urn:ietf:bcp:47',
};
const Languages_Japanese: Coding = {
  code: 'ja',
  display: 'Japanese',
  system: 'urn:ietf:bcp:47',
};
const Languages_Korean: Coding = {
  code: 'ko',
  display: 'Korean',
  system: 'urn:ietf:bcp:47',
};
const Languages_Dutch: Coding = {
  code: 'nl',
  display: 'Dutch',
  system: 'urn:ietf:bcp:47',
};
const Languages_DutchBelgium: Coding = {
  code: 'nl-BE',
  display: 'Dutch (Belgium)',
  system: 'urn:ietf:bcp:47',
};
const Languages_DutchNetherlands: Coding = {
  code: 'nl-NL',
  display: 'Dutch (Netherlands)',
  system: 'urn:ietf:bcp:47',
};
const Languages_Norwegian: Coding = {
  code: 'no',
  display: 'Norwegian',
  system: 'urn:ietf:bcp:47',
};
const Languages_NorwegianNorway: Coding = {
  code: 'no-NO',
  display: 'Norwegian (Norway)',
  system: 'urn:ietf:bcp:47',
};
const Languages_Punjabi: Coding = {
  code: 'pa',
  display: 'Punjabi',
  system: 'urn:ietf:bcp:47',
};
const Languages_Polish: Coding = {
  code: 'pl',
  display: 'Polish',
  system: 'urn:ietf:bcp:47',
};
const Languages_Portuguese: Coding = {
  code: 'pt',
  display: 'Portuguese',
  system: 'urn:ietf:bcp:47',
};
const Languages_PortugueseBrazil: Coding = {
  code: 'pt-BR',
  display: 'Portuguese (Brazil)',
  system: 'urn:ietf:bcp:47',
};
const Languages_Russian: Coding = {
  code: 'ru',
  display: 'Russian',
  system: 'urn:ietf:bcp:47',
};
const Languages_RussianRussia: Coding = {
  code: 'ru-RU',
  display: 'Russian (Russia)',
  system: 'urn:ietf:bcp:47',
};
const Languages_Serbian: Coding = {
  code: 'sr',
  display: 'Serbian',
  system: 'urn:ietf:bcp:47',
};
const Languages_SerbianSerbia: Coding = {
  code: 'sr-RS',
  display: 'Serbian (Serbia)',
  system: 'urn:ietf:bcp:47',
};
const Languages_Swedish: Coding = {
  code: 'sv',
  display: 'Swedish',
  system: 'urn:ietf:bcp:47',
};
const Languages_SwedishSweden: Coding = {
  code: 'sv-SE',
  display: 'Swedish (Sweden)',
  system: 'urn:ietf:bcp:47',
};
const Languages_Telegu: Coding = {
  code: 'te',
  display: 'Telegu',
  system: 'urn:ietf:bcp:47',
};
const Languages_Chinese: Coding = {
  code: 'zh',
  display: 'Chinese',
  system: 'urn:ietf:bcp:47',
};
const Languages_ChineseChina: Coding = {
  code: 'zh-CN',
  display: 'Chinese (China)',
  system: 'urn:ietf:bcp:47',
};
const Languages_ChineseHongKong: Coding = {
  code: 'zh-HK',
  display: 'Chinese (Hong Kong)',
  system: 'urn:ietf:bcp:47',
};
const Languages_ChineseSingapore: Coding = {
  code: 'zh-SG',
  display: 'Chinese (Singapore)',
  system: 'urn:ietf:bcp:47',
};
const Languages_ChineseTaiwan: Coding = {
  code: 'zh-TW',
  display: 'Chinese (Taiwan)',
  system: 'urn:ietf:bcp:47',
};
/**
 * This value set includes common codes from BCP-47 (http://tools.ietf.org/html/bcp47)
 */
export const Languages = {
  Arabic: Languages_Arabic,
  Bengali: Languages_Bengali,
  Czech: Languages_Czech,
  Danish: Languages_Danish,
  German: Languages_German,
  GermanAustria: Languages_GermanAustria,
  GermanSwitzerland: Languages_GermanSwitzerland,
  GermanGermany: Languages_GermanGermany,
  Greek: Languages_Greek,
  English: Languages_English,
  EnglishAustralia: Languages_EnglishAustralia,
  EnglishCanada: Languages_EnglishCanada,
  EnglishGreatBritain: Languages_EnglishGreatBritain,
  EnglishIndia: Languages_EnglishIndia,
  EnglishNewZeland: Languages_EnglishNewZeland,
  EnglishSingapore: Languages_EnglishSingapore,
  EnglishUnitedStates: Languages_EnglishUnitedStates,
  Spanish: Languages_Spanish,
  SpanishArgentina: Languages_SpanishArgentina,
  SpanishSpain: Languages_SpanishSpain,
  SpanishUruguay: Languages_SpanishUruguay,
  Finnish: Languages_Finnish,
  French: Languages_French,
  FrenchBelgium: Languages_FrenchBelgium,
  FrenchSwitzerland: Languages_FrenchSwitzerland,
  FrenchFrance: Languages_FrenchFrance,
  Frysian: Languages_Frysian,
  FrysianNetherlands: Languages_FrysianNetherlands,
  Hindi: Languages_Hindi,
  Croatian: Languages_Croatian,
  Italian: Languages_Italian,
  ItalianSwitzerland: Languages_ItalianSwitzerland,
  ItalianItaly: Languages_ItalianItaly,
  Japanese: Languages_Japanese,
  Korean: Languages_Korean,
  Dutch: Languages_Dutch,
  DutchBelgium: Languages_DutchBelgium,
  DutchNetherlands: Languages_DutchNetherlands,
  Norwegian: Languages_Norwegian,
  NorwegianNorway: Languages_NorwegianNorway,
  Punjabi: Languages_Punjabi,
  Polish: Languages_Polish,
  Portuguese: Languages_Portuguese,
  PortugueseBrazil: Languages_PortugueseBrazil,
  Russian: Languages_Russian,
  RussianRussia: Languages_RussianRussia,
  Serbian: Languages_Serbian,
  SerbianSerbia: Languages_SerbianSerbia,
  Swedish: Languages_Swedish,
  SwedishSweden: Languages_SwedishSweden,
  Telegu: Languages_Telegu,
  Chinese: Languages_Chinese,
  ChineseChina: Languages_ChineseChina,
  ChineseHongKong: Languages_ChineseHongKong,
  ChineseSingapore: Languages_ChineseSingapore,
  ChineseTaiwan: Languages_ChineseTaiwan,
};
const LdlcholesterolCodes_CholesterolInLDLMassVolumeInSerumOrPlasmaByCalculation: Coding = {
  code: '13457-7',
  display: 'Cholesterol in LDL [Mass/volume] in Serum or Plasma by calculation',
  system: 'http://loinc.org',
};
const LdlcholesterolCodes_LDLCholesterolAssay: Coding = {
  code: '18262-6',
  display: 'LDL Cholesterol (Assay)',
  system: 'http://loinc.org',
};
/**
 * LDL Cholesterol codes - measured or calculated
 */
export const LdlcholesterolCodes = {
  CholesterolInLDLMassVolumeInSerumOrPlasmaByCalculation: LdlcholesterolCodes_CholesterolInLDLMassVolumeInSerumOrPlasmaByCalculation,
  LDLCholesterolAssay: LdlcholesterolCodes_LDLCholesterolAssay,
};
const library_type_AssetCollection: Coding = {
  code: 'asset-collection',
  display: 'Asset Collection',
  system: 'http://terminology.hl7.org/CodeSystem/library-type',
};
const library_type_LogicLibrary: Coding = {
  code: 'logic-library',
  display: 'Logic Library',
  system: 'http://terminology.hl7.org/CodeSystem/library-type',
};
const library_type_ModelDefinition: Coding = {
  code: 'model-definition',
  display: 'Model Definition',
  system: 'http://terminology.hl7.org/CodeSystem/library-type',
};
const library_type_ModuleDefinition: Coding = {
  code: 'module-definition',
  display: 'Module Definition',
  system: 'http://terminology.hl7.org/CodeSystem/library-type',
};
/**
 * The type of knowledge asset this library contains.
 */
export const LibraryType = {
  /**
   * The resource is a collection of knowledge assets.
   */
  AssetCollection: library_type_AssetCollection,
  /**
   * The resource is a shareable library of formalized knowledge.
   */
  LogicLibrary: library_type_LogicLibrary,
  /**
   * The resource is a definition of an information model.
   */
  ModelDefinition: library_type_ModelDefinition,
  /**
   * The resource defines the dependencies, parameters, and data requirements for a particular module or evaluation context.
   */
  ModuleDefinition: library_type_ModuleDefinition,
};
const linkage_type_AlternateRecord: Coding = {
  code: 'alternate',
  display: 'Alternate Record',
  system: 'http://hl7.org/fhir/linkage-type',
};
const linkage_type_HistoricalObsoleteRecord: Coding = {
  code: 'historical',
  display: 'Historical/Obsolete Record',
  system: 'http://hl7.org/fhir/linkage-type',
};
const linkage_type_SourceOfTruth: Coding = {
  code: 'source',
  display: 'Source of Truth',
  system: 'http://hl7.org/fhir/linkage-type',
};
/**
 * Used to distinguish different roles a resource can play within a set of linked resources.
 */
export const LinkageType = {
  /**
   * The resource represents an alternative view of the underlying event/condition/etc.  The resource may still be actively maintained, even though it is not considered to be the source of truth.
   */
  AlternateRecord: linkage_type_AlternateRecord,
  /**
   * The resource represents an obsolete record of the underlying event/condition/etc.  It is not expected to be actively maintained.
   */
  HistoricalObsoleteRecord: linkage_type_HistoricalObsoleteRecord,
  /**
   * The resource represents the "source of truth" (from the perspective of this Linkage resource) for the underlying event/condition/etc.
   */
  SourceOfTruth: linkage_type_SourceOfTruth,
};
const link_type_Refer: Coding = {
  code: 'refer',
  display: 'Refer',
  system: 'http://hl7.org/fhir/link-type',
};
const link_type_ReplacedBy: Coding = {
  code: 'replaced-by',
  display: 'Replaced-by',
  system: 'http://hl7.org/fhir/link-type',
};
const link_type_Replaces: Coding = {
  code: 'replaces',
  display: 'Replaces',
  system: 'http://hl7.org/fhir/link-type',
};
const link_type_SeeAlso: Coding = {
  code: 'seealso',
  display: 'See also',
  system: 'http://hl7.org/fhir/link-type',
};
/**
 * The type of link between this patient resource and another patient resource.
 */
export const LinkType = {
  /**
   * The patient resource containing this link is in use and valid but not considered the main source of information about a patient. The link points forward to another patient resource that should be consulted to retrieve additional patient information.
   */
  Refer: link_type_Refer,
  /**
   * The patient resource containing this link must no longer be used. The link points forward to another patient resource that must be used in lieu of the patient resource that contains this link.
   */
  ReplacedBy: link_type_ReplacedBy,
  /**
   * The patient resource containing this link is the current active patient record. The link points back to an inactive patient resource that has been merged into this resource, and should be consulted to retrieve additional referenced information.
   */
  Replaces: link_type_Replaces,
  /**
   * The patient resource containing this link is in use and valid, but points to another patient resource that is known to contain data about the same person. Data in this resource might overlap or contradict information found in the other patient resource. This link does not indicate any relative importance of the resources concerned, and both should be regarded as equally valid.
   */
  SeeAlso: link_type_SeeAlso,
};
const list_empty_reason_Closed: Coding = {
  code: 'closed',
  display: 'Closed',
  system: 'http://terminology.hl7.org/CodeSystem/list-empty-reason',
};
const list_empty_reason_NilKnown: Coding = {
  code: 'nilknown',
  display: 'Nil Known',
  system: 'http://terminology.hl7.org/CodeSystem/list-empty-reason',
};
const list_empty_reason_NotAsked: Coding = {
  code: 'notasked',
  display: 'Not Asked',
  system: 'http://terminology.hl7.org/CodeSystem/list-empty-reason',
};
const list_empty_reason_NotStarted: Coding = {
  code: 'notstarted',
  display: 'Not Started',
  system: 'http://terminology.hl7.org/CodeSystem/list-empty-reason',
};
const list_empty_reason_Unavailable: Coding = {
  code: 'unavailable',
  display: 'Unavailable',
  system: 'http://terminology.hl7.org/CodeSystem/list-empty-reason',
};
const list_empty_reason_InformationWithheld: Coding = {
  code: 'withheld',
  display: 'Information Withheld',
  system: 'http://terminology.hl7.org/CodeSystem/list-empty-reason',
};
/**
 * General reasons for a list to be empty. Reasons are either related to a summary list (i.e. problem or medication list) or to a workflow related list (i.e. consultation list).
 */
export const ListEmptyReason = {
  /**
   * This list has now closed or has ceased to be relevant or useful.
   */
  Closed: list_empty_reason_Closed,
  /**
   * Clinical judgment that there are no known items for this list after reasonable investigation. Note that this a positive statement by a clinical user, and not a default position asserted by a computer system in the lack of other information. Example uses:  * For allergies: the patient or patient's agent/guardian has asserted that he/she is not aware of any allergies (NKA - nil known allergies)  * For medications: the patient or patient's agent/guardian has asserted that the patient is known to be taking no medications  * For diagnoses, problems and procedures: the patient or patient's agent/guardian has asserted that there is no known event to record.
   */
  NilKnown: list_empty_reason_NilKnown,
  /**
   * The investigation to find out whether there are items for this list has not occurred.
   */
  NotAsked: list_empty_reason_NotAsked,
  /**
   * The work to populate this list has not yet begun.
   */
  NotStarted: list_empty_reason_NotStarted,
  /**
   * Information to populate this list cannot be obtained; e.g. unconscious patient.
   */
  Unavailable: list_empty_reason_Unavailable,
  /**
   * The content of the list was not provided due to privacy or confidentiality concerns. Note that it should not be assumed that this means that the particular information in question was withheld due to its contents - it can also be a policy decision.
   */
  InformationWithheld: list_empty_reason_InformationWithheld,
};
const list_example_codes_AdverseReactions: Coding = {
  code: 'adverserxns',
  display: 'Adverse Reactions',
  system: 'http://terminology.hl7.org/CodeSystem/list-example-use-codes',
};
const list_example_codes_Alerts: Coding = {
  code: 'alerts',
  display: 'Alerts',
  system: 'http://terminology.hl7.org/CodeSystem/list-example-use-codes',
};
const list_example_codes_Allergies: Coding = {
  code: 'allergies',
  display: 'Allergies',
  system: 'http://terminology.hl7.org/CodeSystem/list-example-use-codes',
};
const list_example_codes_MedicationList: Coding = {
  code: 'medications',
  display: 'Medication List',
  system: 'http://terminology.hl7.org/CodeSystem/list-example-use-codes',
};
const list_example_codes_CarePlans: Coding = {
  code: 'plans',
  display: 'Care Plans',
  system: 'http://terminology.hl7.org/CodeSystem/list-example-use-codes',
};
const list_example_codes_ProblemList: Coding = {
  code: 'problems',
  display: 'Problem List',
  system: 'http://terminology.hl7.org/CodeSystem/list-example-use-codes',
};
const list_example_codes_Protocols: Coding = {
  code: 'protocols',
  display: 'Protocols',
  system: 'http://terminology.hl7.org/CodeSystem/list-example-use-codes',
};
const list_example_codes_WaitingList: Coding = {
  code: 'waiting',
  display: 'Waiting List',
  system: 'http://terminology.hl7.org/CodeSystem/list-example-use-codes',
};
const list_example_codes_Worklist: Coding = {
  code: 'worklist',
  display: 'Worklist',
  system: 'http://terminology.hl7.org/CodeSystem/list-example-use-codes',
};
/**
 * Example use codes for the List resource - typical kinds of use.
 */
export const ListExampleCodes = {
  /**
   * A list of part adverse reactions.
   */
  AdverseReactions: list_example_codes_AdverseReactions,
  /**
   * A list of alerts for the patient.
   */
  Alerts: list_example_codes_Alerts,
  /**
   * A list of Allergies for the patient.
   */
  Allergies: list_example_codes_Allergies,
  /**
   * A list of medication statements for the patient.
   */
  MedicationList: list_example_codes_MedicationList,
  /**
   * A set of care plans that apply in a particular context of care.
   */
  CarePlans: list_example_codes_CarePlans,
  /**
   * A list of problems that the patient is known of have (or have had in the past).
   */
  ProblemList: list_example_codes_ProblemList,
  /**
   * A set of protocols to be followed.
   */
  Protocols: list_example_codes_Protocols,
  /**
   * A list of items waiting for an event (perhaps a surgical patient waiting list).
   */
  WaitingList: list_example_codes_WaitingList,
  /**
   * A list of items that constitute a set of work to be performed (typically this code would be specialized for more specific uses, such as a ward round list).
   */
  Worklist: list_example_codes_Worklist,
};
const list_item_flag_Unchanged: Coding = {
  code: '01',
  display: 'Unchanged',
  system: 'urn:oid:1.2.36.1.2001.1001.101.104.16592',
};
const list_item_flag_Changed: Coding = {
  code: '02',
  display: 'Changed',
  system: 'urn:oid:1.2.36.1.2001.1001.101.104.16592',
};
const list_item_flag_Cancelled: Coding = {
  code: '03',
  display: 'Cancelled',
  system: 'urn:oid:1.2.36.1.2001.1001.101.104.16592',
};
const list_item_flag_Prescribed: Coding = {
  code: '04',
  display: 'Prescribed',
  system: 'urn:oid:1.2.36.1.2001.1001.101.104.16592',
};
const list_item_flag_Ceased: Coding = {
  code: '05',
  display: 'Ceased',
  system: 'urn:oid:1.2.36.1.2001.1001.101.104.16592',
};
const list_item_flag_Suspended: Coding = {
  code: '06',
  display: 'Suspended',
  system: 'urn:oid:1.2.36.1.2001.1001.101.104.16592',
};
/**
 * Example Item Flags for the List Resource. In this case, these are the kind of flags that would be used on a medication list at the end of a consultation.
 */
export const ListItemFlag = {
  /**
   * No change has been made to the status of this medicine item.
   */
  Unchanged: list_item_flag_Unchanged,
  /**
   * The medicine item has changed. The change may be described in an extension (not defined yet)
   */
  Changed: list_item_flag_Changed,
  /**
   * The prescription for this medicine item was cancelled by an authorized health care provider. The patient may be advised to complete the course of the prescribed medicine. This advice is a clinical decision made based on assessment of the patient's clinical condition.
   */
  Cancelled: list_item_flag_Cancelled,
  /**
   * A new medicine item has been prescribed
   */
  Prescribed: list_item_flag_Prescribed,
  /**
   * Administration of this medication item that the patient is currently taking is stopped or recommended to be stopped (i.e. instructed to be ceased by a health care provider). This cessation is anticipated to be permanent. The Change Description should describe the reason for cessation. Example uses: the medication in question is considered ineffective or has caused serious adverse effects. This value applies both to the cessation of a medication that is prescribed by another healthcare provider or patient self-administration of OTC medicines.
   */
  Ceased: list_item_flag_Ceased,
  /**
   * Administration of this medication item that the patient is currently taking is on hold, or instructed or recommended by a health care provider to be temporarily stopped, or subject to clinical review (i.e. the stop may be temporary or permanent depending on the outcome of clinical review), or temporarily suspended as a pre-requisite to certain surgical or diagnostic procedures.
   */
  Suspended: list_item_flag_Suspended,
};
const list_mode_ChangeList: Coding = {
  code: 'changes',
  display: 'Change List',
  system: 'http://hl7.org/fhir/list-mode',
};
const list_mode_SnapshotList: Coding = {
  code: 'snapshot',
  display: 'Snapshot List',
  system: 'http://hl7.org/fhir/list-mode',
};
const list_mode_WorkingList: Coding = {
  code: 'working',
  display: 'Working List',
  system: 'http://hl7.org/fhir/list-mode',
};
/**
 * The processing mode that applies to this list.
 */
export const ListMode = {
  /**
   * A point-in-time list that shows what changes have been made or recommended.  E.g. a discharge medication list showing what was added and removed during an encounter.
   */
  ChangeList: list_mode_ChangeList,
  /**
   * This list was prepared as a snapshot. It should not be assumed to be current.
   */
  SnapshotList: list_mode_SnapshotList,
  /**
   * This list is the master list, maintained in an ongoing fashion with regular updates as the real world list it is tracking changes.
   */
  WorkingList: list_mode_WorkingList,
};
const list_order_SortedAlphabetically: Coding = {
  code: 'alphabetic',
  display: 'Sorted Alphabetically',
  system: 'http://terminology.hl7.org/CodeSystem/list-order',
};
const list_order_SortedByCategory: Coding = {
  code: 'category',
  display: 'Sorted by Category',
  system: 'http://terminology.hl7.org/CodeSystem/list-order',
};
const list_order_SortedByItemDate: Coding = {
  code: 'entry-date',
  display: 'Sorted by Item Date',
  system: 'http://terminology.hl7.org/CodeSystem/list-order',
};
const list_order_SortedByEventDate: Coding = {
  code: 'event-date',
  display: 'Sorted by Event Date',
  system: 'http://terminology.hl7.org/CodeSystem/list-order',
};
const list_order_SortedByPatient: Coding = {
  code: 'patient',
  display: 'Sorted by Patient',
  system: 'http://terminology.hl7.org/CodeSystem/list-order',
};
const list_order_SortedByPriority: Coding = {
  code: 'priority',
  display: 'Sorted by Priority',
  system: 'http://terminology.hl7.org/CodeSystem/list-order',
};
const list_order_SortedBySystem: Coding = {
  code: 'system',
  display: 'Sorted by System',
  system: 'http://terminology.hl7.org/CodeSystem/list-order',
};
const list_order_SortedByUser: Coding = {
  code: 'user',
  display: 'Sorted by User',
  system: 'http://terminology.hl7.org/CodeSystem/list-order',
};
/**
 * Base values for the order of the items in a list resource.
 */
export const ListOrder = {
  /**
   * The list is sorted alphabetically by an unspecified property of the items in the list.
   */
  SortedAlphabetically: list_order_SortedAlphabetically,
  /**
   * The list is sorted categorically by an unspecified property of the items in the list.
   */
  SortedByCategory: list_order_SortedByCategory,
  /**
   * The list is sorted by the date the item was added to the list. Note that the date added to the list is not explicit in the list itself.
   */
  SortedByItemDate: list_order_SortedByItemDate,
  /**
   * The list is sorted by the data of the event. This can be used when the list has items which are dates with past or future events.
   */
  SortedByEventDate: list_order_SortedByEventDate,
  /**
   * The list is sorted by patient, with items for each patient grouped together.
   */
  SortedByPatient: list_order_SortedByPatient,
  /**
   * The list is sorted by priority. The exact method in which priority has been determined is not specified.
   */
  SortedByPriority: list_order_SortedByPriority,
  /**
   * The list was sorted by the system. The criteria the user used are not specified; define additional codes to specify a particular order (or use other defined codes).
   */
  SortedBySystem: list_order_SortedBySystem,
  /**
   * The list was sorted by a user. The criteria the user used are not specified.
   */
  SortedByUser: list_order_SortedByUser,
};
const list_status_Current: Coding = {
  code: 'current',
  display: 'Current',
  system: 'http://hl7.org/fhir/list-status',
};
const list_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered In Error',
  system: 'http://hl7.org/fhir/list-status',
};
const list_status_Retired: Coding = {
  code: 'retired',
  display: 'Retired',
  system: 'http://hl7.org/fhir/list-status',
};
/**
 * The current state of the list.
 */
export const ListStatus = {
  /**
   * The list is considered to be an active part of the patient's record.
   */
  Current: list_status_Current,
  /**
   * The list was never accurate.  It is retained for medico-legal purposes only.
   */
  EnteredInError: list_status_EnteredInError,
  /**
   * The list is "old" and should no longer be considered accurate or relevant.
   */
  Retired: list_status_Retired,
};
const location_mode_Instance: Coding = {
  code: 'instance',
  display: 'Instance',
  system: 'http://hl7.org/fhir/location-mode',
};
const location_mode_Kind: Coding = {
  code: 'kind',
  display: 'Kind',
  system: 'http://hl7.org/fhir/location-mode',
};
/**
 * Indicates whether a resource instance represents a specific location or a class of locations.
 */
export const LocationMode = {
  /**
   * The Location resource represents a specific instance of a location (e.g. Operating Theatre 1A).
   */
  Instance: location_mode_Instance,
  /**
   * The Location represents a class of locations (e.g. Any Operating Theatre) although this class of locations could be constrained within a specific boundary (such as organization, or parent location, address etc.).
   */
  Kind: location_mode_Kind,
};
const location_physical_type_Area: Coding = {
  code: 'area',
  display: 'Area',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
const location_physical_type_Bed: Coding = {
  code: 'bd',
  display: 'Bed',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
const location_physical_type_Building: Coding = {
  code: 'bu',
  display: 'Building',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
const location_physical_type_Cabinet: Coding = {
  code: 'ca',
  display: 'Cabinet',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
const location_physical_type_Corridor: Coding = {
  code: 'co',
  display: 'Corridor',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
const location_physical_type_House: Coding = {
  code: 'ho',
  display: 'House',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
const location_physical_type_Jurisdiction: Coding = {
  code: 'jdn',
  display: 'Jurisdiction',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
const location_physical_type_Level: Coding = {
  code: 'lvl',
  display: 'Level',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
const location_physical_type_Road: Coding = {
  code: 'rd',
  display: 'Road',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
const location_physical_type_Room: Coding = {
  code: 'ro',
  display: 'Room',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
const location_physical_type_Site: Coding = {
  code: 'si',
  display: 'Site',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
const location_physical_type_Vehicle: Coding = {
  code: 've',
  display: 'Vehicle',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
const location_physical_type_Ward: Coding = {
  code: 'wa',
  display: 'Ward',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
const location_physical_type_Wing: Coding = {
  code: 'wi',
  display: 'Wing',
  system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
};
/**
 * This example value set defines a set of codes that can be used to indicate the physical form of the Location.
 */
export const LocationPhysicalType = {
  /**
   * A defined physical boundary of something, such as a flood risk zone, region, postcode
   */
  Area: location_physical_type_Area,
  /**
   * A space that is allocated for sleeping/laying on. This is not the physical bed/trolley that may be moved about, but the space it may occupy.
   */
  Bed: location_physical_type_Bed,
  /**
   * Any Building or structure. This may contain rooms, corridors, wings, etc. It might not have walls, or a roof, but is considered a defined/allocated space.
   */
  Building: location_physical_type_Building,
  /**
   * A container that can store goods, equipment, medications or other items.
   */
  Cabinet: location_physical_type_Cabinet,
  /**
   * Any corridor within a Building, that may connect rooms.
   */
  Corridor: location_physical_type_Corridor,
  /**
   * A residential dwelling. Usually used to reference a location that a person/patient may reside.
   */
  House: location_physical_type_House,
  /**
   * A wide scope that covers a conceptual domain, such as a Nation (Country wide community or Federal Government - e.g. Ministry of Health),  Province or State (community or Government), Business (throughout the enterprise), Nation with a business scope of an agency (e.g. CDC, FDA etc.) or a Business segment (UK Pharmacy), not just an physical boundary
   */
  Jurisdiction: location_physical_type_Jurisdiction,
  /**
   * A Level in a multi-level Building/Structure.
   */
  Level: location_physical_type_Level,
  /**
   * A defined path to travel between 2 points that has a known name.
   */
  Road: location_physical_type_Road,
  /**
   * A space that is allocated as a room, it may have walls/roof etc., but does not require these.
   */
  Room: location_physical_type_Room,
  /**
   * A collection of buildings or other locations such as a site or a campus.
   */
  Site: location_physical_type_Site,
  /**
   * A means of transportation.
   */
  Vehicle: location_physical_type_Vehicle,
  /**
   * A Ward is a section of a medical facility that may contain rooms and other types of location.
   */
  Ward: location_physical_type_Ward,
  /**
   * A Wing within a Building, this often contains levels, rooms and corridors.
   */
  Wing: location_physical_type_Wing,
};
const location_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/location-status',
};
const location_status_Inactive: Coding = {
  code: 'inactive',
  display: 'Inactive',
  system: 'http://hl7.org/fhir/location-status',
};
const location_status_Suspended: Coding = {
  code: 'suspended',
  display: 'Suspended',
  system: 'http://hl7.org/fhir/location-status',
};
/**
 * Indicates whether the location is still in use.
 */
export const LocationStatus = {
  /**
   * The location is operational.
   */
  Active: location_status_Active,
  /**
   * The location is no longer used.
   */
  Inactive: location_status_Inactive,
  /**
   * The location is temporarily closed.
   */
  Suspended: location_status_Suspended,
};
const map_context_type_Type: Coding = {
  code: 'type',
  display: 'Type',
  system: 'http://hl7.org/fhir/map-context-type',
};
const map_context_type_Variable: Coding = {
  code: 'variable',
  display: 'Variable',
  system: 'http://hl7.org/fhir/map-context-type',
};
/**
 * How to interpret the context.
 */
export const MapContextType = {
  /**
   * The context specifies a type.
   */
  Type: map_context_type_Type,
  /**
   * The context specifies a variable.
   */
  Variable: map_context_type_Variable,
};
const map_group_type_mode_NotADefault: Coding = {
  code: 'none',
  display: 'Not a Default',
  system: 'http://hl7.org/fhir/map-group-type-mode',
};
const map_group_type_mode_DefaultForTypePlusCombination: Coding = {
  code: 'type-and-types',
  display: 'Default for type + combination',
  system: 'http://hl7.org/fhir/map-group-type-mode',
};
const map_group_type_mode_DefaultForTypeCombination: Coding = {
  code: 'types',
  display: 'Default for Type Combination',
  system: 'http://hl7.org/fhir/map-group-type-mode',
};
/**
 * If this is the default rule set to apply for the source type, or this combination of types.
 */
export const MapGroupTypeMode = {
  /**
   * This group is not a default group for the types.
   */
  NotADefault: map_group_type_mode_NotADefault,
  /**
   * This group is a default mapping group for the specified types.
   */
  DefaultForTypePlusCombination: map_group_type_mode_DefaultForTypePlusCombination,
  /**
   * This group is a default mapping group for the specified types and for the primary source type.
   */
  DefaultForTypeCombination: map_group_type_mode_DefaultForTypeCombination,
};
const map_input_mode_SourceInstance: Coding = {
  code: 'source',
  display: 'Source Instance',
  system: 'http://hl7.org/fhir/map-input-mode',
};
const map_input_mode_TargetInstance: Coding = {
  code: 'target',
  display: 'Target Instance',
  system: 'http://hl7.org/fhir/map-input-mode',
};
/**
 * Mode for this instance of data.
 */
export const MapInputMode = {
  /**
   * Names an input instance used a source for mapping.
   */
  SourceInstance: map_input_mode_SourceInstance,
  /**
   * Names an instance that is being populated.
   */
  TargetInstance: map_input_mode_TargetInstance,
};
const map_model_mode_ProducedStructureDefinition: Coding = {
  code: 'produced',
  display: 'Produced Structure Definition',
  system: 'http://hl7.org/fhir/map-model-mode',
};
const map_model_mode_QueriedStructureDefinition: Coding = {
  code: 'queried',
  display: 'Queried Structure Definition',
  system: 'http://hl7.org/fhir/map-model-mode',
};
const map_model_mode_SourceStructureDefinition: Coding = {
  code: 'source',
  display: 'Source Structure Definition',
  system: 'http://hl7.org/fhir/map-model-mode',
};
const map_model_mode_TargetStructureDefinition: Coding = {
  code: 'target',
  display: 'Target Structure Definition',
  system: 'http://hl7.org/fhir/map-model-mode',
};
/**
 * How the referenced structure is used in this mapping.
 */
export const MapModelMode = {
  /**
   * This structure describes an instance that the mapping engine may ask to create that is used a target of data.
   */
  ProducedStructureDefinition: map_model_mode_ProducedStructureDefinition,
  /**
   * This structure describes an instance that the mapping engine may ask for that is used a source of data.
   */
  QueriedStructureDefinition: map_model_mode_QueriedStructureDefinition,
  /**
   * This structure describes an instance passed to the mapping engine that is used a source of data.
   */
  SourceStructureDefinition: map_model_mode_SourceStructureDefinition,
  /**
   * This structure describes an instance passed to the mapping engine that is used a target of data.
   */
  TargetStructureDefinition: map_model_mode_TargetStructureDefinition,
};
const map_source_list_mode_First: Coding = {
  code: 'first',
  display: 'First',
  system: 'http://hl7.org/fhir/map-source-list-mode',
};
const map_source_list_mode_Last: Coding = {
  code: 'last',
  display: 'Last',
  system: 'http://hl7.org/fhir/map-source-list-mode',
};
const map_source_list_mode_AllButTheFirst: Coding = {
  code: 'not_first',
  display: 'All but the first',
  system: 'http://hl7.org/fhir/map-source-list-mode',
};
const map_source_list_mode_AllButTheLast: Coding = {
  code: 'not_last',
  display: 'All but the last',
  system: 'http://hl7.org/fhir/map-source-list-mode',
};
const map_source_list_mode_EnforceOnlyOne: Coding = {
  code: 'only_one',
  display: 'Enforce only one',
  system: 'http://hl7.org/fhir/map-source-list-mode',
};
/**
 * If field is a list, how to manage the source.
 */
export const MapSourceListMode = {
  /**
   * Only process this rule for the first in the list.
   */
  First: map_source_list_mode_First,
  /**
   * Only process this rule for the last in the list.
   */
  Last: map_source_list_mode_Last,
  /**
   * Process this rule for all but the first.
   */
  AllButTheFirst: map_source_list_mode_AllButTheFirst,
  /**
   * Process this rule for all but the last.
   */
  AllButTheLast: map_source_list_mode_AllButTheLast,
  /**
   * Only process this rule is there is only item.
   */
  EnforceOnlyOne: map_source_list_mode_EnforceOnlyOne,
};
const map_target_list_mode_Collate: Coding = {
  code: 'collate',
  display: 'Collate',
  system: 'http://hl7.org/fhir/map-target-list-mode',
};
const map_target_list_mode_First: Coding = {
  code: 'first',
  display: 'First',
  system: 'http://hl7.org/fhir/map-target-list-mode',
};
const map_target_list_mode_Last: Coding = {
  code: 'last',
  display: 'Last',
  system: 'http://hl7.org/fhir/map-target-list-mode',
};
const map_target_list_mode_Share: Coding = {
  code: 'share',
  display: 'Share',
  system: 'http://hl7.org/fhir/map-target-list-mode',
};
/**
 * If field is a list, how to manage the production.
 */
export const MapTargetListMode = {
  /**
   * re-use the first item in the list, and keep adding content to it.
   */
  Collate: map_target_list_mode_Collate,
  /**
   * when the target list is being assembled, the items for this rule go first. If more than one rule defines a first item (for a given instance of mapping) then this is an error.
   */
  First: map_target_list_mode_First,
  /**
   * when the target list is being assembled, the items for this rule go last. If more than one rule defines a last item (for a given instance of mapping) then this is an error.
   */
  Last: map_target_list_mode_Last,
  /**
   * the target instance is shared with the target instances generated by another rule (up to the first common n items, then create new ones).
   */
  Share: map_target_list_mode_Share,
};
const map_transform_Append: Coding = {
  code: 'append',
  display: 'append',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_C: Coding = {
  code: 'c',
  display: 'c',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Cast: Coding = {
  code: 'cast',
  display: 'cast',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Cc: Coding = {
  code: 'cc',
  display: 'cc',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Copy: Coding = {
  code: 'copy',
  display: 'copy',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Cp: Coding = {
  code: 'cp',
  display: 'cp',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Create: Coding = {
  code: 'create',
  display: 'create',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_DateOp: Coding = {
  code: 'dateOp',
  display: 'dateOp',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Escape: Coding = {
  code: 'escape',
  display: 'escape',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Evaluate: Coding = {
  code: 'evaluate',
  display: 'evaluate',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Id: Coding = {
  code: 'id',
  display: 'id',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Pointer: Coding = {
  code: 'pointer',
  display: 'pointer',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Qty: Coding = {
  code: 'qty',
  display: 'qty',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Reference: Coding = {
  code: 'reference',
  display: 'reference',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Translate: Coding = {
  code: 'translate',
  display: 'translate',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Truncate: Coding = {
  code: 'truncate',
  display: 'truncate',
  system: 'http://hl7.org/fhir/map-transform',
};
const map_transform_Uuid: Coding = {
  code: 'uuid',
  display: 'uuid',
  system: 'http://hl7.org/fhir/map-transform',
};
/**
 * How data is copied/created.
 */
export const MapTransform = {
  /**
   * append(source...) - source is element or string.
   */
  Append: map_transform_Append,
  /**
   * Create a Coding. Parameters = (system. Code[, display]).
   */
  C: map_transform_C,
  /**
   * cast(source, type?) - case source from one type to another. target type can be left as implicit if there is one and only one target type known.
   */
  Cast: map_transform_Cast,
  /**
   * Create a CodeableConcept. Parameters = (text) or (system. Code[, display]).
   */
  Cc: map_transform_Cc,
  /**
   * copy(source).
   */
  Copy: map_transform_Copy,
  /**
   * Create a contact details. Parameters = (value) or (system, value). If no system is provided, the system should be inferred from the content of the value.
   */
  Cp: map_transform_Cp,
  /**
   * create(type : string) - type is passed through to the application on the standard API, and must be known by it.
   */
  Create: map_transform_Create,
  /**
   * Perform a date operation. *Parameters to be documented*.
   */
  DateOp: map_transform_DateOp,
  /**
   * escape(source, fmt1, fmt2) - change source from one kind of escaping to another (plain, java, xml, json). note that this is for when the string itself is escaped.
   */
  Escape: map_transform_Escape,
  /**
   * Execute the supplied FHIRPath expression and use the value returned by that.
   */
  Evaluate: map_transform_Evaluate,
  /**
   * Create an identifier. Parameters = (system, value[, type]) where type is a code from the identifier type value set.
   */
  Id: map_transform_Id,
  /**
   * Return the appropriate string to put in a reference that refers to the resource provided as a parameter.
   */
  Pointer: map_transform_Pointer,
  /**
   * Create a quantity. Parameters = (text) or (value, unit, [system, code]) where text is the natural representation e.g. [comparator]value[space]unit.
   */
  Qty: map_transform_Qty,
  /**
   * reference(source : object) - return a string that references the provided tree properly.
   */
  Reference: map_transform_Reference,
  /**
   * translate(source, uri_of_map) - use the translate operation.
   */
  Translate: map_transform_Translate,
  /**
   * truncate(source, length) - source must be stringy type.
   */
  Truncate: map_transform_Truncate,
  /**
   * Generate a random UUID (in lowercase). No Parameters.
   */
  Uuid: map_transform_Uuid,
};
const v3_MaritalStatus_Annulled: Coding = {
  code: 'A',
  display: 'Annulled',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',
};
const v3_MaritalStatus_Divorced: Coding = {
  code: 'D',
  display: 'Divorced',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',
};
const v3_MaritalStatus_Interlocutory: Coding = {
  code: 'I',
  display: 'Interlocutory',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',
};
const v3_MaritalStatus_LegallySeparated: Coding = {
  code: 'L',
  display: 'Legally Separated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',
};
const v3_MaritalStatus_Married: Coding = {
  code: 'M',
  display: 'Married',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',
};
const v3_MaritalStatus_Polygamous: Coding = {
  code: 'P',
  display: 'Polygamous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',
};
const v3_MaritalStatus_NeverMarried: Coding = {
  code: 'S',
  display: 'Never Married',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',
};
const v3_MaritalStatus_DomesticPartner: Coding = {
  code: 'T',
  display: 'Domestic partner',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',
};
const v3_MaritalStatus_Unmarried: Coding = {
  code: 'U',
  display: 'unmarried',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',
};
const v3_MaritalStatus_Widowed: Coding = {
  code: 'W',
  display: 'Widowed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',
};
/**
 * This value set defines the set of codes that can be used to indicate the marital status of a person.
 */
export const MaritalStatus = {
  /**
   * Marriage contract has been declared null and to not have existed
   */
  Annulled_v3_MaritalStatus: v3_MaritalStatus_Annulled,
  /**
   * Marriage contract has been declared dissolved and inactive
   */
  Divorced_v3_MaritalStatus: v3_MaritalStatus_Divorced,
  /**
   * Subject to an Interlocutory Decree.
   */
  Interlocutory_v3_MaritalStatus: v3_MaritalStatus_Interlocutory,
  /**
   * Legally Separated
   */
  LegallySeparated_v3_MaritalStatus: v3_MaritalStatus_LegallySeparated,
  /**
   * A current marriage contract is active
   */
  Married_v3_MaritalStatus: v3_MaritalStatus_Married,
  /**
   * More than 1 current spouse
   */
  Polygamous_v3_MaritalStatus: v3_MaritalStatus_Polygamous,
  /**
   * No marriage contract has ever been entered
   */
  NeverMarried_v3_MaritalStatus: v3_MaritalStatus_NeverMarried,
  /**
   * Person declares that a domestic partner relationship exists.
   */
  DomesticPartner_v3_MaritalStatus: v3_MaritalStatus_DomesticPartner,
  /**
   * Currently not in a marriage contract.
   */
  Unmarried_v3_MaritalStatus: v3_MaritalStatus_Unmarried,
  /**
   * Description:A proper value is applicable, but not known.
   *
   *
   *                            Usage Notes: This means the actual value is not known.  If the only thing that is unknown is how to properly express the value in the necessary constraints (value set, datatype, etc.), then the OTH or UNC flavor should be used.  No properties should be included for a datatype with this property unless:
   *
   *
   *                            Those properties themselves directly translate to a semantic of "unknown".  (E.g. a local code sent as a translation that conveys 'unknown')
   *                            Those properties further qualify the nature of what is unknown.  (E.g. specifying a use code of "H" and a URL prefix of "tel:" to convey that it is the home phone number that is unknown.)
   */
  Unknown_v3_NullFlavor: v3_NullFlavor_Unknown,
  /**
   * The spouse has died
   */
  Widowed_v3_MaritalStatus: v3_MaritalStatus_Widowed,
};
const match_grade_CertainMatch: Coding = {
  code: 'certain',
  display: 'Certain Match',
  system: 'http://terminology.hl7.org/CodeSystem/match-grade',
};
const match_grade_CertainlyNotAMatch: Coding = {
  code: 'certainly-not',
  display: 'Certainly Not a Match',
  system: 'http://terminology.hl7.org/CodeSystem/match-grade',
};
const match_grade_PossibleMatch: Coding = {
  code: 'possible',
  display: 'Possible Match',
  system: 'http://terminology.hl7.org/CodeSystem/match-grade',
};
const match_grade_ProbableMatch: Coding = {
  code: 'probable',
  display: 'Probable Match',
  system: 'http://terminology.hl7.org/CodeSystem/match-grade',
};
/**
 * A Master Patient Index (MPI) assessment of whether a candidate patient record is a match or not.
 */
export const MatchGrade = {
  /**
   * This record meets the matching criteria to be automatically considered as a full match.
   */
  CertainMatch: match_grade_CertainMatch,
  /**
   * This record is known not to be a match. Note that usually non-matching records are not returned, but in some cases records previously or likely considered as a match may specifically be negated by the matching engine.
   */
  CertainlyNotAMatch: match_grade_CertainlyNotAMatch,
  /**
   * This record may be a matching one. Additional review (e.g. by a human) SHOULD be performed before using this as a match.
   */
  PossibleMatch: match_grade_PossibleMatch,
  /**
   * This record is a close match, but not a certain match. Additional review (e.g. by a human) may be required before using this as a match.
   */
  ProbableMatch: match_grade_ProbableMatch,
};
const measure_data_usage_RiskAdjustmentFactor: Coding = {
  code: 'risk-adjustment-factor',
  display: 'Risk Adjustment Factor',
  system: 'http://terminology.hl7.org/CodeSystem/measure-data-usage',
};
const measure_data_usage_SupplementalData: Coding = {
  code: 'supplemental-data',
  display: 'Supplemental Data',
  system: 'http://terminology.hl7.org/CodeSystem/measure-data-usage',
};
/**
 * The intended usage for supplemental data elements in the measure.
 */
export const MeasureDataUsage = {
  /**
   * The data is intended to be used to calculate and apply a risk adjustment model for the measure.
   */
  RiskAdjustmentFactor: measure_data_usage_RiskAdjustmentFactor,
  /**
   * The data is intended to be provided as additional information alongside the measure results.
   */
  SupplementalData: measure_data_usage_SupplementalData,
};
const measure_improvement_notation_DecreasedScoreIndicatesImprovement: Coding = {
  code: 'decrease',
  display: 'Decreased score indicates improvement',
  system: 'http://terminology.hl7.org/CodeSystem/measure-improvement-notation',
};
const measure_improvement_notation_IncreasedScoreIndicatesImprovement: Coding = {
  code: 'increase',
  display: 'Increased score indicates improvement',
  system: 'http://terminology.hl7.org/CodeSystem/measure-improvement-notation',
};
/**
 * Observation values that indicate what change in a measurement value or score is indicative of an improvement in the measured item or scored issue.
 */
export const MeasureImprovementNotation = {
  /**
   * Improvement is indicated as a decrease in the score or measurement (e.g. Lower score indicates better quality).
   */
  DecreasedScoreIndicatesImprovement: measure_improvement_notation_DecreasedScoreIndicatesImprovement,
  /**
   * Improvement is indicated as an increase in the score or measurement (e.g. Higher score indicates better quality).
   */
  IncreasedScoreIndicatesImprovement: measure_improvement_notation_IncreasedScoreIndicatesImprovement,
};
const measure_population_Denominator: Coding = {
  code: 'denominator',
  display: 'Denominator',
  system: 'http://terminology.hl7.org/CodeSystem/measure-population',
};
const measure_population_DenominatorException: Coding = {
  code: 'denominator-exception',
  display: 'Denominator Exception',
  system: 'http://terminology.hl7.org/CodeSystem/measure-population',
};
const measure_population_DenominatorExclusion: Coding = {
  code: 'denominator-exclusion',
  display: 'Denominator Exclusion',
  system: 'http://terminology.hl7.org/CodeSystem/measure-population',
};
const measure_population_InitialPopulation: Coding = {
  code: 'initial-population',
  display: 'Initial Population',
  system: 'http://terminology.hl7.org/CodeSystem/measure-population',
};
const measure_population_MeasureObservation: Coding = {
  code: 'measure-observation',
  display: 'Measure Observation',
  system: 'http://terminology.hl7.org/CodeSystem/measure-population',
};
const measure_population_MeasurePopulation: Coding = {
  code: 'measure-population',
  display: 'Measure Population',
  system: 'http://terminology.hl7.org/CodeSystem/measure-population',
};
const measure_population_MeasurePopulationExclusion: Coding = {
  code: 'measure-population-exclusion',
  display: 'Measure Population Exclusion',
  system: 'http://terminology.hl7.org/CodeSystem/measure-population',
};
const measure_population_Numerator: Coding = {
  code: 'numerator',
  display: 'Numerator',
  system: 'http://terminology.hl7.org/CodeSystem/measure-population',
};
const measure_population_NumeratorExclusion: Coding = {
  code: 'numerator-exclusion',
  display: 'Numerator Exclusion',
  system: 'http://terminology.hl7.org/CodeSystem/measure-population',
};
/**
 * The type of population.
 */
export const MeasurePopulation = {
  /**
   * The lower portion of a fraction used to calculate a rate, proportion, or ratio. The denominator can be the same as the initial population, or a subset of the initial population to further constrain the population for the purpose of the measure.
   */
  Denominator: measure_population_Denominator,
  /**
   * Denominator exceptions are conditions that should remove a patient or event from the denominator of a measure only if the numerator criteria are not met. Denominator exception allows for adjustment of the calculated score for those providers with higher risk populations. Denominator exception criteria are only used in proportion measures.
   */
  DenominatorException: measure_population_DenominatorException,
  /**
   * Denominator exclusion criteria define patients or events that should be removed from the denominator before determining if numerator criteria are met. Denominator exclusions are used in proportion and ratio measures to help narrow the denominator. For example, patients with bilateral lower extremity amputations would be listed as a denominator exclusion for a measure requiring foot exams.
   */
  DenominatorExclusion: measure_population_DenominatorExclusion,
  /**
   * The initial population refers to all patients or events to be evaluated by a quality measure involving patients who share a common set of specified characterstics. All patients or events counted (for example, as numerator, as denominator) are drawn from the initial population.
   */
  InitialPopulation: measure_population_InitialPopulation,
  /**
   * Defines the individual observation to be performed for each patient or event in the measure population. Measure observations for each case in the population are aggregated to determine the overall measure score for the population.
   */
  MeasureObservation: measure_population_MeasureObservation,
  /**
   * Measure population criteria define the patients or events for which the individual observation for the measure should be taken. Measure populations are used for continuous variable measures rather than numerator and denominator criteria.
   */
  MeasurePopulation: measure_population_MeasurePopulation,
  /**
   * Measure population criteria define the patients or events that should be removed from the measure population before determining the outcome of one or more continuous variables defined for the measure observation. Measure population exclusion criteria are used within continuous variable measures to help narrow the measure population.
   */
  MeasurePopulationExclusion: measure_population_MeasurePopulationExclusion,
  /**
   * The upper portion of a fraction used to calculate a rate, proportion, or ratio. Also called the measure focus, it is the target process, condition, event, or outcome. Numerator criteria are the processes or outcomes expected for each patient, or event defined in the denominator. A numerator statement describes the clinical action that satisfies the conditions of the measure.
   */
  Numerator: measure_population_Numerator,
  /**
   * Numerator exclusion criteria define patients or events to be removed from the numerator. Numerator exclusions are used in proportion and ratio measures to help narrow the numerator (for inverted measures).
   */
  NumeratorExclusion: measure_population_NumeratorExclusion,
};
const measure_report_status_Complete: Coding = {
  code: 'complete',
  display: 'Complete',
  system: 'http://hl7.org/fhir/measure-report-status',
};
const measure_report_status_Error: Coding = {
  code: 'error',
  display: 'Error',
  system: 'http://hl7.org/fhir/measure-report-status',
};
const measure_report_status_Pending: Coding = {
  code: 'pending',
  display: 'Pending',
  system: 'http://hl7.org/fhir/measure-report-status',
};
/**
 * The status of the measure report.
 */
export const MeasureReportStatus = {
  /**
   * The report is complete and ready for use.
   */
  Complete: measure_report_status_Complete,
  /**
   * An error occurred attempting to generate the report.
   */
  Error: measure_report_status_Error,
  /**
   * The report is currently being generated.
   */
  Pending: measure_report_status_Pending,
};
const measure_report_type_DataCollection: Coding = {
  code: 'data-collection',
  display: 'Data Collection',
  system: 'http://hl7.org/fhir/measure-report-type',
};
const measure_report_type_Individual: Coding = {
  code: 'individual',
  display: 'Individual',
  system: 'http://hl7.org/fhir/measure-report-type',
};
const measure_report_type_SubjectList: Coding = {
  code: 'subject-list',
  display: 'Subject List',
  system: 'http://hl7.org/fhir/measure-report-type',
};
const measure_report_type_Summary: Coding = {
  code: 'summary',
  display: 'Summary',
  system: 'http://hl7.org/fhir/measure-report-type',
};
/**
 * The type of the measure report.
 */
export const MeasureReportType = {
  /**
   * A data collection report that contains data-of-interest for the measure.
   */
  DataCollection: measure_report_type_DataCollection,
  /**
   * An individual report that provides information on the performance for a given measure with respect to a single subject.
   */
  Individual: measure_report_type_Individual,
  /**
   * A subject list report that includes a listing of subjects that satisfied each population criteria in the measure.
   */
  SubjectList: measure_report_type_SubjectList,
  /**
   * A summary report that returns the number of members in each population criteria for the measure.
   */
  Summary: measure_report_type_Summary,
};
const measure_scoring_Cohort: Coding = {
  code: 'cohort',
  display: 'Cohort',
  system: 'http://terminology.hl7.org/CodeSystem/measure-scoring',
};
const measure_scoring_ContinuousVariable: Coding = {
  code: 'continuous-variable',
  display: 'Continuous Variable',
  system: 'http://terminology.hl7.org/CodeSystem/measure-scoring',
};
const measure_scoring_Proportion: Coding = {
  code: 'proportion',
  display: 'Proportion',
  system: 'http://terminology.hl7.org/CodeSystem/measure-scoring',
};
const measure_scoring_Ratio: Coding = {
  code: 'ratio',
  display: 'Ratio',
  system: 'http://terminology.hl7.org/CodeSystem/measure-scoring',
};
/**
 * The scoring type of the measure.
 */
export const MeasureScoring = {
  /**
   * The measure is a cohort definition.
   */
  Cohort: measure_scoring_Cohort,
  /**
   * The score is defined by a calculation of some quantity.
   */
  ContinuousVariable: measure_scoring_ContinuousVariable,
  /**
   * The measure score is defined using a proportion.
   */
  Proportion: measure_scoring_Proportion,
  /**
   * The measure score is defined using a ratio.
   */
  Ratio: measure_scoring_Ratio,
};
const measure_type_Composite: Coding = {
  code: 'composite',
  display: 'Composite',
  system: 'http://terminology.hl7.org/CodeSystem/measure-type',
};
const measure_type_Outcome: Coding = {
  code: 'outcome',
  display: 'Outcome',
  system: 'http://terminology.hl7.org/CodeSystem/measure-type',
};
const measure_type_PatientReportedOutcome: Coding = {
  code: 'patient-reported-outcome',
  display: 'Patient Reported Outcome',
  system: 'http://terminology.hl7.org/CodeSystem/measure-type',
};
const measure_type_Process: Coding = {
  code: 'process',
  display: 'Process',
  system: 'http://terminology.hl7.org/CodeSystem/measure-type',
};
const measure_type_Structure: Coding = {
  code: 'structure',
  display: 'Structure',
  system: 'http://terminology.hl7.org/CodeSystem/measure-type',
};
/**
 * The type of measure (includes codes from 2.16.840.1.113883.1.11.20368).
 */
export const MeasureType = {
  /**
   * A measure that combines multiple component measures in to a single quality measure.
   */
  Composite: measure_type_Composite,
  /**
   * A measure that indicates the result of the performance (or non-performance) of a function or process.
   */
  Outcome: measure_type_Outcome,
  /**
   * A measure that focuses on patient-reported information such as patient engagement or patient experience measures.
   */
  PatientReportedOutcome: measure_type_PatientReportedOutcome,
  /**
   * A measure which focuses on a process which leads to a certain outcome, meaning that a scientific basis exists for believing that the process, when executed well, will increase the probability of achieving a desired outcome.
   */
  Process: measure_type_Process,
  /**
   * A measure that focuses on a health care provider's capacity, systems, and processes to provide high-quality care.
   */
  Structure: measure_type_Structure,
};
const med_admin_perform_function_Performer: Coding = {
  code: 'performer',
  display: 'Performer',
  system: 'http://terminology.hl7.org/CodeSystem/med-admin-perform-function',
};
const med_admin_perform_function_Verifier: Coding = {
  code: 'verifier',
  display: 'Verifier',
  system: 'http://terminology.hl7.org/CodeSystem/med-admin-perform-function',
};
const med_admin_perform_function_Witness: Coding = {
  code: 'witness',
  display: 'Witness',
  system: 'http://terminology.hl7.org/CodeSystem/med-admin-perform-function',
};
/**
 * MedicationAdministration Performer Function Codes
 */
export const MedAdminPerformFunction = {
  /**
   * A person, non-person living subject, organization or device that who actually and principally carries out the action
   */
  Performer: med_admin_perform_function_Performer,
  /**
   * A person who verifies the correctness and appropriateness of the service (plan, order, event, etc.) and hence takes on accountability.
   */
  Verifier: med_admin_perform_function_Verifier,
  /**
   * A person witnessing the action happening without doing anything. A witness is not necessarily aware, much less approves of anything stated in the service event. Example for a witness is students watching an operation or an advanced directive witness.
   */
  Witness: med_admin_perform_function_Witness,
};
const media_modality_Diagram: Coding = {
  code: 'diagram',
  display: 'Diagram',
  system: 'http://terminology.hl7.org/CodeSystem/media-modality',
};
const media_modality_FaceScan: Coding = {
  code: 'face',
  display: 'Face Scan',
  system: 'http://terminology.hl7.org/CodeSystem/media-modality',
};
const media_modality_Fax: Coding = {
  code: 'fax',
  display: 'Fax',
  system: 'http://terminology.hl7.org/CodeSystem/media-modality',
};
const media_modality_Fingerprint: Coding = {
  code: 'fingerprint',
  display: 'Fingerprint',
  system: 'http://terminology.hl7.org/CodeSystem/media-modality',
};
const media_modality_IrisScan: Coding = {
  code: 'iris',
  display: 'Iris Scan',
  system: 'http://terminology.hl7.org/CodeSystem/media-modality',
};
const media_modality_PalmScan: Coding = {
  code: 'palm',
  display: 'Palm Scan',
  system: 'http://terminology.hl7.org/CodeSystem/media-modality',
};
const media_modality_RetinaScan: Coding = {
  code: 'retina',
  display: 'Retina Scan',
  system: 'http://terminology.hl7.org/CodeSystem/media-modality',
};
const media_modality_ScannedDocument: Coding = {
  code: 'scan',
  display: 'Scanned Document',
  system: 'http://terminology.hl7.org/CodeSystem/media-modality',
};
/**
 * Detailed information about the type of the image - its kind, purpose, or the kind of equipment used to generate it.
 */
export const MediaModality = {
  /**
   * A diagram. Often used in diagnostic reports
   */
  Diagram: media_modality_Diagram,
  /**
   * A face scan used for identification purposes
   */
  FaceScan: media_modality_FaceScan,
  /**
   * A digital record of a fax document
   */
  Fax: media_modality_Fax,
  /**
   * A finger print scan used for identification purposes
   */
  Fingerprint: media_modality_Fingerprint,
  /**
   * An iris scan used for identification purposes
   */
  IrisScan: media_modality_IrisScan,
  /**
   * A palm scan used for identification purposes
   */
  PalmScan: media_modality_PalmScan,
  /**
   * A retinal image used for identification purposes
   */
  RetinaScan: media_modality_RetinaScan,
  /**
   * A digital scan of a document. This is reserved for when there is not enough metadata to create a document reference
   */
  ScannedDocument: media_modality_ScannedDocument,
};
const media_type_Audio: Coding = {
  code: 'audio',
  display: 'Audio',
  system: 'http://terminology.hl7.org/CodeSystem/media-type',
};
const media_type_Image: Coding = {
  code: 'image',
  display: 'Image',
  system: 'http://terminology.hl7.org/CodeSystem/media-type',
};
const media_type_Video: Coding = {
  code: 'video',
  display: 'Video',
  system: 'http://terminology.hl7.org/CodeSystem/media-type',
};
/**
 * Codes for high level media categories.
 */
export const MediaType = {
  /**
   * The media consists of a sound recording
   */
  Audio: media_type_Audio,
  /**
   * The media consists of one or more unmoving images, including photographs, computer-generated graphs and charts, and scanned documents
   */
  Image: media_type_Image,
  /**
   * The media consists of a series of frames that capture a moving image
   */
  Video: media_type_Video,
};
const medication_admin_category_Community: Coding = {
  code: 'community',
  display: 'Community',
  system: 'http://terminology.hl7.org/CodeSystem/medication-admin-category',
};
const medication_admin_category_Inpatient: Coding = {
  code: 'inpatient',
  display: 'Inpatient',
  system: 'http://terminology.hl7.org/CodeSystem/medication-admin-category',
};
const medication_admin_category_Outpatient: Coding = {
  code: 'outpatient',
  display: 'Outpatient',
  system: 'http://terminology.hl7.org/CodeSystem/medication-admin-category',
};
/**
 * MedicationAdministration Category Codes
 */
export const MedicationAdminCategory = {
  /**
   * Includes administrations by the patient in their home (this would include long term care or nursing homes, hospices, etc.)
   */
  Community: medication_admin_category_Community,
  /**
   * Includes administrations in an inpatient or acute care setting
   */
  Inpatient: medication_admin_category_Inpatient,
  /**
   * Includes administrations in an outpatient setting (for example, Emergency Department, Outpatient Clinic, Outpatient Surgery, Doctor's office)
   */
  Outpatient: medication_admin_category_Outpatient,
};
const medication_admin_status_InProgress: Coding = {
  code: 'in-progress',
  display: 'In Progress',
  system: 'http://terminology.hl7.org/CodeSystem/medication-admin-status',
};
const medication_admin_status_NotDone: Coding = {
  code: 'not-done',
  display: 'Not Done',
  system: 'http://terminology.hl7.org/CodeSystem/medication-admin-status',
};
const medication_admin_status_OnHold: Coding = {
  code: 'on-hold',
  display: 'On Hold',
  system: 'http://terminology.hl7.org/CodeSystem/medication-admin-status',
};
const medication_admin_status_Stopped: Coding = {
  code: 'stopped',
  display: 'Stopped',
  system: 'http://terminology.hl7.org/CodeSystem/medication-admin-status',
};
const medication_admin_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/medication-admin-status',
};
/**
 * MedicationAdministration Status Codes
 */
export const MedicationAdminStatus = {
  /**
   * All actions that are implied by the administration have occurred.
   */
  Completed: medication_admin_status_Completed,
  /**
   * The administration was entered in error and therefore nullified.
   */
  EnteredInError: medication_admin_status_EnteredInError,
  /**
   * The administration has started but has not yet completed.
   */
  InProgress: medication_admin_status_InProgress,
  /**
   * The administration was terminated prior to any impact on the subject (though preparatory actions may have been taken)
   */
  NotDone: medication_admin_status_NotDone,
  /**
   * Actions implied by the administration have been temporarily halted, but are expected to continue later. May also be called 'suspended'.
   */
  OnHold: medication_admin_status_OnHold,
  /**
   * Actions implied by the administration have been permanently halted, before all of them occurred.
   */
  Stopped: medication_admin_status_Stopped,
  /**
   * The authoring system does not know which of the status values currently applies for this request. Note: This concept is not to be used for 'other' - one of the listed statuses is presumed to apply, it's just not known which one.
   */
  Unknown: medication_admin_status_Unknown,
};
const medicationdispense_category_Community: Coding = {
  code: 'community',
  display: 'Community',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-category',
};
const medicationdispense_category_Discharge: Coding = {
  code: 'discharge',
  display: 'Discharge',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-category',
};
const medicationdispense_category_Inpatient: Coding = {
  code: 'inpatient',
  display: 'Inpatient',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-category',
};
const medicationdispense_category_Outpatient: Coding = {
  code: 'outpatient',
  display: 'Outpatient',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-category',
};
/**
 * MedicationDispense Category Codes
 */
export const MedicationdispenseCategory = {
  /**
   * Includes dispenses for medications to be administered or consumed by the patient in their home (this would include long term care or nursing homes, hospices, etc.).
   */
  Community: medicationdispense_category_Community,
  /**
   * Includes dispenses for medications created when the patient is being released from a facility.
   */
  Discharge: medicationdispense_category_Discharge,
  /**
   * Includes dispenses for medications to be administered or consumed in an inpatient or acute care setting.
   */
  Inpatient: medicationdispense_category_Inpatient,
  /**
   * Includes dispenses for medications to be administered or consumed in an outpatient setting (for example, Emergency Department, Outpatient Clinic, Outpatient Surgery, Doctor's office).
   */
  Outpatient: medicationdispense_category_Outpatient,
};
const medicationdispense_performer_function_Checker: Coding = {
  code: 'checker',
  display: 'Checker',
  system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-performer-function',
};
const medicationdispense_performer_function_DataEnterer: Coding = {
  code: 'dataenterer',
  display: 'Data Enterer',
  system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-performer-function',
};
const medicationdispense_performer_function_FinalChecker: Coding = {
  code: 'finalchecker',
  display: 'Final Checker',
  system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-performer-function',
};
const medicationdispense_performer_function_Packager: Coding = {
  code: 'packager',
  display: 'Packager',
  system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-performer-function',
};
/**
 * MedicationDispense Performer Function Codes
 */
export const MedicationdispensePerformerFunction = {
  /**
   * Performed initial quality assurance on the prepared medication
   */
  Checker: medicationdispense_performer_function_Checker,
  /**
   * Recorded the details of the request
   */
  DataEnterer: medicationdispense_performer_function_DataEnterer,
  /**
   * Performed the final quality assurance on the prepared medication against the request. Typically, this is a pharmacist function.
   */
  FinalChecker: medicationdispense_performer_function_FinalChecker,
  /**
   * Prepared the medication.
   */
  Packager: medicationdispense_performer_function_Packager,
};
const medicationdispense_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-status',
};
const medicationdispense_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-status',
};
const medicationdispense_status_Declined: Coding = {
  code: 'declined',
  display: 'Declined',
  system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-status',
};
const medicationdispense_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-status',
};
const medicationdispense_status_InProgress: Coding = {
  code: 'in-progress',
  display: 'In Progress',
  system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-status',
};
const medicationdispense_status_OnHold: Coding = {
  code: 'on-hold',
  display: 'On Hold',
  system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-status',
};
const medicationdispense_status_Preparation: Coding = {
  code: 'preparation',
  display: 'Preparation',
  system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-status',
};
const medicationdispense_status_Stopped: Coding = {
  code: 'stopped',
  display: 'Stopped',
  system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-status',
};
const medicationdispense_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-status',
};
/**
 * MedicationDispense Status Codes
 */
export const MedicationdispenseStatus = {
  /**
   * The dispensed product was not and will never be picked up by the patient.
   */
  Cancelled: medicationdispense_status_Cancelled,
  /**
   * The dispensed product has been picked up.
   */
  Completed: medicationdispense_status_Completed,
  /**
   * The dispense was declined and not performed.
   */
  Declined: medicationdispense_status_Declined,
  /**
   * The dispense was entered in error and therefore nullified.
   */
  EnteredInError: medicationdispense_status_EnteredInError,
  /**
   * The dispensed product is ready for pickup.
   */
  InProgress: medicationdispense_status_InProgress,
  /**
   * The dispense process is paused while waiting for an external event to reactivate the dispense.  For example, new stock has arrived or the prescriber has called.
   */
  OnHold: medicationdispense_status_OnHold,
  /**
   * The core event has not started yet, but some staging activities have begun (e.g. initial compounding or packaging of medication). Preparation stages may be tracked for billing purposes.
   */
  Preparation: medicationdispense_status_Preparation,
  /**
   * Actions implied by the dispense have been permanently halted, before all of them occurred.
   */
  Stopped: medicationdispense_status_Stopped,
  /**
   * The authoring system does not know which of the status values applies for this medication dispense.  Note: this concept is not to be used for other - one of the listed statuses is presumed to apply, it's just now known which one.
   */
  Unknown: medicationdispense_status_Unknown,
};
const medicationdispense_status_reason_TryAnotherTreatmentFirst: Coding = {
  code: 'altchoice',
  display: 'Try another treatment first',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_PrescriptionRequestRequiresClarification: Coding = {
  code: 'clarif',
  display: 'Prescription/Request requires clarification',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_DrugLevelTooHigh: Coding = {
  code: 'drughigh',
  display: 'Drug level too high',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_OrderStopped: Coding = {
  code: 'frr01',
  display: 'Order Stopped',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_StaleDatedOrder: Coding = {
  code: 'frr02',
  display: 'Stale-dated Order',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_IncompleteData: Coding = {
  code: 'frr03',
  display: 'Incomplete data',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_ProductUnavailable: Coding = {
  code: 'frr04',
  display: 'Product unavailable',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_EthicalReligious: Coding = {
  code: 'frr05',
  display: 'Ethical/religious',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_UnableToProvideCare: Coding = {
  code: 'frr06',
  display: 'Unable to provide care',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_AdmissionToHospital: Coding = {
  code: 'hospadm',
  display: 'Admission to hospital',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_LabInterferenceIssues: Coding = {
  code: 'labint',
  display: 'Lab interference issues',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_PatientNotAvailable: Coding = {
  code: 'non-avail',
  display: 'Patient not available',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_DrugNotAvailableOffMarket: Coding = {
  code: 'offmarket',
  display: 'Drug not available - off market',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_DrugNotAvailableOutOfStock: Coding = {
  code: 'outofstock',
  display: 'Drug not available - out of stock',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_PatientIsPregnantOrBreastfeeding: Coding = {
  code: 'preg',
  display: 'Patient is pregnant or breastfeeding',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_Allergy: Coding = {
  code: 'saig',
  display: 'Allergy',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_DrugInteractsWithAnotherDrug: Coding = {
  code: 'sddi',
  display: 'Drug interacts with another drug',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_DuplicateTherapy: Coding = {
  code: 'sdupther',
  display: 'Duplicate therapy',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_SuspectedIntolerance: Coding = {
  code: 'sintol',
  display: 'Suspected intolerance',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_PatientScheduledForSurgery: Coding = {
  code: 'surg',
  display: 'Patient scheduled for surgery',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
const medicationdispense_status_reason_Washout: Coding = {
  code: 'washout',
  display: 'Washout',
  system: 'http://terminology.hl7.org/fhir/CodeSystem/medicationdispense-status-reason',
};
/**
 * MedicationDispense Status Codes
 */
export const MedicationdispenseStatusReason = {
  /**
   * This therapy has been ordered as a backup to a preferred therapy. This order will be released when and if the preferred therapy is unsuccessful.
   */
  TryAnotherTreatmentFirst: medicationdispense_status_reason_TryAnotherTreatmentFirst,
  /**
   * Clarification is required before the order can be acted upon.
   */
  PrescriptionRequestRequiresClarification: medicationdispense_status_reason_PrescriptionRequestRequiresClarification,
  /**
   * The current level of the medication in the patient's system is too high. The medication is suspended to allow the level to subside to a safer level.
   */
  DrugLevelTooHigh: medicationdispense_status_reason_DrugLevelTooHigh,
  /**
   * The order has been stopped by the prescriber but this fact has not necessarily captured electronically. Example: A verbal stop, a fax, etc.
   */
  OrderStopped: medicationdispense_status_reason_OrderStopped,
  /**
   * Order has not been fulfilled within a reasonable amount of time, and might not be current.
   */
  StaleDatedOrder: medicationdispense_status_reason_StaleDatedOrder,
  /**
   * Data needed to safely act on the order which was expected to become available independent of the order is not yet available. Example: Lab results, diagnostic imaging, etc.
   */
  IncompleteData: medicationdispense_status_reason_IncompleteData,
  /**
   * Product not available or manufactured. Cannot supply.
   */
  ProductUnavailable: medicationdispense_status_reason_ProductUnavailable,
  /**
   * The dispenser has ethical, religious or moral objections to fulfilling the order/dispensing the product.
   */
  EthicalReligious: medicationdispense_status_reason_EthicalReligious,
  /**
   * Fulfiller not able to provide appropriate care associated with fulfilling the order. Example: Therapy requires ongoing monitoring by fulfiller and fulfiller will be ending practice, leaving town, unable to schedule necessary time, etc.
   */
  UnableToProvideCare: medicationdispense_status_reason_UnableToProvideCare,
  /**
   * The patient has been admitted to a care facility and their community medications are suspended until hospital discharge.
   */
  AdmissionToHospital: medicationdispense_status_reason_AdmissionToHospital,
  /**
   * The therapy would interfere with a planned lab test and the therapy is being withdrawn until the test is completed.
   */
  LabInterferenceIssues: medicationdispense_status_reason_LabInterferenceIssues,
  /**
   * Patient not available for a period of time due to a scheduled therapy, leave of absence or other reason.
   */
  PatientNotAvailable: medicationdispense_status_reason_PatientNotAvailable,
  /**
   * Drug no longer marketed Cannot supply.
   */
  DrugNotAvailableOffMarket: medicationdispense_status_reason_DrugNotAvailableOffMarket,
  /**
   * Drug out of stock. Cannot supply.
   */
  DrugNotAvailableOutOfStock: medicationdispense_status_reason_DrugNotAvailableOutOfStock,
  /**
   * The patient is pregnant or breast feeding. The therapy will be resumed when the pregnancy is complete and the patient is no longer breastfeeding.
   */
  PatientIsPregnantOrBreastfeeding: medicationdispense_status_reason_PatientIsPregnantOrBreastfeeding,
  /**
   * The patient is believed to be allergic to a substance that is part of the therapy and the therapy is being temporarily withdrawn to confirm.
   */
  Allergy: medicationdispense_status_reason_Allergy,
  /**
   * The drug interacts with a short-term treatment that is more urgently required. This order will be resumed when the short-term treatment is complete.
   */
  DrugInteractsWithAnotherDrug: medicationdispense_status_reason_DrugInteractsWithAnotherDrug,
  /**
   * Another short-term co-occurring therapy fulfills the same purpose as this therapy. This therapy will be resumed when the co-occuring therapy is complete.
   */
  DuplicateTherapy: medicationdispense_status_reason_DuplicateTherapy,
  /**
   * The patient is believed to have an intolerance to a substance that is part of the therapy and the therapy is being temporarily withdrawn to confirm.
   */
  SuspectedIntolerance: medicationdispense_status_reason_SuspectedIntolerance,
  /**
   * The drug is contraindicated for patients receiving surgery and the patient is scheduled to be admitted for surgery in the near future. The drug will be resumed when the patient has sufficiently recovered from the surgery.
   */
  PatientScheduledForSurgery: medicationdispense_status_reason_PatientScheduledForSurgery,
  /**
   * The patient was previously receiving a medication contraindicated with the current medication. The current medication will remain on hold until the prior medication has been cleansed from their system.
   */
  Washout: medicationdispense_status_reason_Washout,
};
const medicationknowledge_characteristic_Coating: Coding = {
  code: 'coating',
  display: 'Coating',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-characteristic',
};
const medicationknowledge_characteristic_Color: Coding = {
  code: 'color',
  display: 'Color',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-characteristic',
};
const medicationknowledge_characteristic_ImprintCode: Coding = {
  code: 'imprintcd',
  display: 'Imprint Code',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-characteristic',
};
const medicationknowledge_characteristic_Logo: Coding = {
  code: 'logo',
  display: 'Logo',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-characteristic',
};
const medicationknowledge_characteristic_Scoring: Coding = {
  code: 'scoring',
  display: 'Scoring',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-characteristic',
};
const medicationknowledge_characteristic_Shape: Coding = {
  code: 'shape',
  display: 'Shape',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-characteristic',
};
const medicationknowledge_characteristic_Size: Coding = {
  code: 'size',
  display: 'Size',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-characteristic',
};
/**
 * MedicationKnowledge Characteristic Codes
 */
export const MedicationknowledgeCharacteristic = {
  /**
   * Description of the coating of the product
   */
  Coating: medicationknowledge_characteristic_Coating,
  /**
   * Description of the color of the product
   */
  Color: medicationknowledge_characteristic_Color,
  /**
   * Identyifying marks on product
   */
  ImprintCode: medicationknowledge_characteristic_ImprintCode,
  /**
   * Description of the Logo of the product
   */
  Logo: medicationknowledge_characteristic_Logo,
  /**
   * Description of the scoring of the product
   */
  Scoring: medicationknowledge_characteristic_Scoring,
  /**
   * Description of the shape of the product
   */
  Shape: medicationknowledge_characteristic_Shape,
  /**
   * Description of size of the product
   */
  Size: medicationknowledge_characteristic_Size,
};
const medicationknowledge_package_type_Ampule: Coding = {
  code: 'amp',
  display: 'Ampule',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Bag: Coding = {
  code: 'bag',
  display: 'Bag',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_BlisterPack: Coding = {
  code: 'blstrpk',
  display: 'Blister Pack',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Bottle: Coding = {
  code: 'bot',
  display: 'Bottle',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Box: Coding = {
  code: 'box',
  display: 'Box',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Can: Coding = {
  code: 'can',
  display: 'Can',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Cartridge: Coding = {
  code: 'cart',
  display: 'Cartridge',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Disk: Coding = {
  code: 'disk',
  display: 'Disk',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Dosette: Coding = {
  code: 'doset',
  display: 'Dosette',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Jar: Coding = {
  code: 'jar',
  display: 'Jar',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Jug: Coding = {
  code: 'jug',
  display: 'Jug',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Minim: Coding = {
  code: 'minim',
  display: 'Minim',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_NebuleAmp: Coding = {
  code: 'nebamp',
  display: 'Nebule Amp',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Ovule: Coding = {
  code: 'ovul',
  display: 'Ovule',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Pouch: Coding = {
  code: 'pch',
  display: 'Pouch',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Packet: Coding = {
  code: 'pkt',
  display: 'Packet',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Sashet: Coding = {
  code: 'sash',
  display: 'Sashet',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Strip: Coding = {
  code: 'strip',
  display: 'Strip',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Tin: Coding = {
  code: 'tin',
  display: 'Tin',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Tub: Coding = {
  code: 'tub',
  display: 'Tub',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Tube: Coding = {
  code: 'tube',
  display: 'Tube',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
const medicationknowledge_package_type_Vial: Coding = {
  code: 'vial',
  display: 'Vial',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-package-type',
};
/**
 * MedicationKnowledge Package Type Codes
 */
export const MedicationknowledgePackageType = {
  Ampule: medicationknowledge_package_type_Ampule,
  Bag: medicationknowledge_package_type_Bag,
  BlisterPack: medicationknowledge_package_type_BlisterPack,
  Bottle: medicationknowledge_package_type_Bottle,
  Box: medicationknowledge_package_type_Box,
  Can: medicationknowledge_package_type_Can,
  Cartridge: medicationknowledge_package_type_Cartridge,
  Disk: medicationknowledge_package_type_Disk,
  Dosette: medicationknowledge_package_type_Dosette,
  Jar: medicationknowledge_package_type_Jar,
  Jug: medicationknowledge_package_type_Jug,
  Minim: medicationknowledge_package_type_Minim,
  NebuleAmp: medicationknowledge_package_type_NebuleAmp,
  Ovule: medicationknowledge_package_type_Ovule,
  Pouch: medicationknowledge_package_type_Pouch,
  Packet: medicationknowledge_package_type_Packet,
  Sashet: medicationknowledge_package_type_Sashet,
  Strip: medicationknowledge_package_type_Strip,
  Tin: medicationknowledge_package_type_Tin,
  Tub: medicationknowledge_package_type_Tub,
  Tube: medicationknowledge_package_type_Tube,
  Vial: medicationknowledge_package_type_Vial,
};
const medicationknowledge_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-status',
};
const medicationknowledge_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-status',
};
const medicationknowledge_status_Inactive: Coding = {
  code: 'inactive',
  display: 'Inactive',
  system: 'http://terminology.hl7.org/CodeSystem/medicationknowledge-status',
};
/**
 * MedicationKnowledge Status Codes
 */
export const MedicationknowledgeStatus = {
  /**
   * The medication is available for use.
   */
  Active: medicationknowledge_status_Active,
  /**
   * The medication was entered in error.
   */
  EnteredInError: medicationknowledge_status_EnteredInError,
  /**
   * The medication is not available for use.
   */
  Inactive: medicationknowledge_status_Inactive,
};
const medicationrequest_category_Community: Coding = {
  code: 'community',
  display: 'Community',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-category',
};
const medicationrequest_category_Discharge: Coding = {
  code: 'discharge',
  display: 'Discharge',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-category',
};
const medicationrequest_category_Inpatient: Coding = {
  code: 'inpatient',
  display: 'Inpatient',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-category',
};
const medicationrequest_category_Outpatient: Coding = {
  code: 'outpatient',
  display: 'Outpatient',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-category',
};
/**
 * MedicationRequest Category Codes
 */
export const MedicationrequestCategory = {
  /**
   * Includes requests for medications to be administered or consumed by the patient in their home (this would include long term care or nursing homes, hospices, etc.)
   */
  Community: medicationrequest_category_Community,
  /**
   * Includes requests for medications created when the patient is being released from a facility
   */
  Discharge: medicationrequest_category_Discharge,
  /**
   * Includes requests for medications to be administered or consumed in an inpatient or acute care setting
   */
  Inpatient: medicationrequest_category_Inpatient,
  /**
   * Includes requests for medications to be administered or consumed in an outpatient setting (for example, Emergency Department, Outpatient Clinic, Outpatient Surgery, Doctor's office)
   */
  Outpatient: medicationrequest_category_Outpatient,
};
const medicationrequest_course_of_therapy_ShortCourseAcuteTherapy: Coding = {
  code: 'acute',
  display: 'Short course (acute) therapy',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-course-of-therapy',
};
const medicationrequest_course_of_therapy_ContinuousLongTermTherapy: Coding = {
  code: 'continuous',
  display: 'Continuous long term therapy',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-course-of-therapy',
};
const medicationrequest_course_of_therapy_Seasonal: Coding = {
  code: 'seasonal',
  display: 'Seasonal',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-course-of-therapy',
};
/**
 * MedicationRequest Course of Therapy Codes
 */
export const MedicationrequestCourseOfTherapy = {
  /**
   * A medication which the patient is only expected to consume for the duration of the current order and which is not expected to be renewed.
   */
  ShortCourseAcuteTherapy: medicationrequest_course_of_therapy_ShortCourseAcuteTherapy,
  /**
   * A medication which is expected to be continued beyond the present order and which the patient should be assumed to be taking unless explicitly stopped.
   */
  ContinuousLongTermTherapy: medicationrequest_course_of_therapy_ContinuousLongTermTherapy,
  /**
   * A medication which is expected to be used on a part time basis at certain times of the year
   */
  Seasonal: medicationrequest_course_of_therapy_Seasonal,
};
const medicationrequest_intent_FillerOrder: Coding = {
  code: 'filler-order',
  display: 'Filler Order',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-intent',
};
const medicationrequest_intent_InstanceOrder: Coding = {
  code: 'instance-order',
  display: 'Instance Order',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-intent',
};
const medicationrequest_intent_Option: Coding = {
  code: 'option',
  display: 'Option',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-intent',
};
const medicationrequest_intent_Order: Coding = {
  code: 'order',
  display: 'Order',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-intent',
};
const medicationrequest_intent_OriginalOrder: Coding = {
  code: 'original-order',
  display: 'Original Order',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-intent',
};
const medicationrequest_intent_Plan: Coding = {
  code: 'plan',
  display: 'Plan',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-intent',
};
const medicationrequest_intent_Proposal: Coding = {
  code: 'proposal',
  display: 'Proposal',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-intent',
};
const medicationrequest_intent_ReflexOrder: Coding = {
  code: 'reflex-order',
  display: 'Reflex Order',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-intent',
};
/**
 * MedicationRequest Intent Codes
 */
export const MedicationrequestIntent = {
  /**
   * The request represents the view of an authorization instantiated by a fulfilling system representing the details of the fulfiller's intention to act upon a submitted order.
   */
  FillerOrder: medicationrequest_intent_FillerOrder,
  /**
   * The request represents an instance for the particular order, for example a medication administration record.
   */
  InstanceOrder: medicationrequest_intent_InstanceOrder,
  /**
   * The request represents a component or option for a RequestGroup that establishes timing, conditionality and/or  other constraints among a set of requests.
   */
  Option: medicationrequest_intent_Option,
  /**
   * The request represents a request/demand and authorization for action
   */
  Order: medicationrequest_intent_Order,
  /**
   * The request represents the original authorization for the medication request.
   */
  OriginalOrder: medicationrequest_intent_OriginalOrder,
  /**
   * The request represents an intention to ensure something occurs without providing an authorization for others to act.
   */
  Plan: medicationrequest_intent_Plan,
  /**
   * The request is a suggestion made by someone/something that doesn't have an intention to ensure it occurs and without providing an authorization to act
   */
  Proposal: medicationrequest_intent_Proposal,
  /**
   * The request represents an automatically generated supplemental authorization for action based on a parent authorization together with initial results of the action taken against that parent authorization..
   */
  ReflexOrder: medicationrequest_intent_ReflexOrder,
};
const medicationrequest_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-status',
};
const medicationrequest_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-status',
};
const medicationrequest_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-status',
};
const medicationrequest_status_Draft: Coding = {
  code: 'draft',
  display: 'Draft',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-status',
};
const medicationrequest_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-status',
};
const medicationrequest_status_OnHold: Coding = {
  code: 'on-hold',
  display: 'On Hold',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-status',
};
const medicationrequest_status_Stopped: Coding = {
  code: 'stopped',
  display: 'Stopped',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-status',
};
const medicationrequest_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/CodeSystem/medicationrequest-status',
};
/**
 * MedicationRequest Status Codes
 */
export const MedicationrequestStatus = {
  /**
   * The prescription is 'actionable', but not all actions that are implied by it have occurred yet.
   */
  Active: medicationrequest_status_Active,
  /**
   * The prescription has been withdrawn before any administrations have occurred
   */
  Cancelled: medicationrequest_status_Cancelled,
  /**
   * All actions that are implied by the prescription have occurred.
   */
  Completed: medicationrequest_status_Completed,
  /**
   * The prescription is not yet 'actionable', e.g. it is a work in progress, requires sign-off, verification or needs to be run through decision support process.
   */
  Draft: medicationrequest_status_Draft,
  /**
   * Some of the actions that are implied by the medication request may have occurred.  For example, the medication may have been dispensed and the patient may have taken some of the medication.  Clinical decision support systems should take this status into account
   */
  EnteredInError: medicationrequest_status_EnteredInError,
  /**
   * Actions implied by the prescription are to be temporarily halted, but are expected to continue later.  May also be called 'suspended'.
   */
  OnHold: medicationrequest_status_OnHold,
  /**
   * Actions implied by the prescription are to be permanently halted, before all of the administrations occurred. This should not be used if the original order was entered in error
   */
  Stopped: medicationrequest_status_Stopped,
  /**
   * The authoring/source system does not know which of the status values currently applies for this observation. Note: This concept is not to be used for 'other' - one of the listed statuses is presumed to apply, but the authoring/source system does not know which.
   */
  Unknown: medicationrequest_status_Unknown,
};
const medicationrequest_status_reason_TryAnotherTreatmentFirst: Coding = {
  code: 'altchoice',
  display: 'Try another treatment first',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-status-reason',
};
const medicationrequest_status_reason_PrescriptionRequiresClarification: Coding = {
  code: 'clarif',
  display: 'Prescription requires clarification',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-status-reason',
};
const medicationrequest_status_reason_DrugLevelTooHigh: Coding = {
  code: 'drughigh',
  display: 'Drug level too high',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-status-reason',
};
const medicationrequest_status_reason_AdmissionToHospital: Coding = {
  code: 'hospadm',
  display: 'Admission to hospital',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-status-reason',
};
const medicationrequest_status_reason_LabInterferenceIssues: Coding = {
  code: 'labint',
  display: 'Lab interference issues',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-status-reason',
};
const medicationrequest_status_reason_PatientNotAvailable: Coding = {
  code: 'non-avail',
  display: 'Patient not available',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-status-reason',
};
const medicationrequest_status_reason_ParentIsPregnantBreastFeeding: Coding = {
  code: 'preg',
  display: 'Parent is pregnant/breast feeding',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-status-reason',
};
const medicationrequest_status_reason_Allergy: Coding = {
  code: 'salg',
  display: 'Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-status-reason',
};
const medicationrequest_status_reason_DrugInteractsWithAnotherDrug: Coding = {
  code: 'sddi',
  display: 'Drug interacts with another drug',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-status-reason',
};
const medicationrequest_status_reason_DuplicateTherapy: Coding = {
  code: 'sdupther',
  display: 'Duplicate therapy',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-status-reason',
};
const medicationrequest_status_reason_SuspectedIntolerance: Coding = {
  code: 'sintol',
  display: 'Suspected intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-status-reason',
};
const medicationrequest_status_reason_PatientScheduledForSurgery: Coding = {
  code: 'surg',
  display: 'Patient scheduled for surgery.',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-status-reason',
};
const medicationrequest_status_reason_WaitingForOldDrugToWashOut: Coding = {
  code: 'washout',
  display: 'Waiting for old drug to wash out',
  system: 'http://terminology.hl7.org/CodeSystem/medicationrequest-status-reason',
};
/**
 * MedicationRequest Status Reason Codes
 */
export const MedicationrequestStatusReason = {
  /**
   * This therapy has been ordered as a backup to a preferred therapy. This order will be released when and if the preferred therapy is unsuccessful.
   */
  TryAnotherTreatmentFirst: medicationrequest_status_reason_TryAnotherTreatmentFirst,
  /**
   * Clarification is required before the order can be acted upon.
   */
  PrescriptionRequiresClarification: medicationrequest_status_reason_PrescriptionRequiresClarification,
  /**
   * The current level of the medication in the patient's system is too high. The medication is suspended to allow the level to subside to a safer level.
   */
  DrugLevelTooHigh: medicationrequest_status_reason_DrugLevelTooHigh,
  /**
   * The patient has been admitted to a care facility and their community medications are suspended until hospital discharge.
   */
  AdmissionToHospital: medicationrequest_status_reason_AdmissionToHospital,
  /**
   * The therapy would interfere with a planned lab test and the therapy is being withdrawn until the test is completed.
   */
  LabInterferenceIssues: medicationrequest_status_reason_LabInterferenceIssues,
  /**
   * Patient not available for a period of time due to a scheduled therapy, leave of absence or other reason.
   */
  PatientNotAvailable: medicationrequest_status_reason_PatientNotAvailable,
  /**
   * The patient is pregnant or breast feeding. The therapy will be resumed when the pregnancy is complete and the patient is no longer breastfeeding.
   */
  ParentIsPregnantBreastFeeding: medicationrequest_status_reason_ParentIsPregnantBreastFeeding,
  /**
   * The patient is believed to be allergic to a substance that is part of the therapy and the therapy is being temporarily withdrawn to confirm.
   */
  Allergy: medicationrequest_status_reason_Allergy,
  /**
   * The drug interacts with a short-term treatment that is more urgently required. This order will be resumed when the short-term treatment is complete.
   */
  DrugInteractsWithAnotherDrug: medicationrequest_status_reason_DrugInteractsWithAnotherDrug,
  /**
   * The drug interacts with a short-term treatment that is more urgently required. This order will be resumed when the short-term treatment is complete.
   */
  DuplicateTherapy: medicationrequest_status_reason_DuplicateTherapy,
  /**
   * The drug interacts with a short-term treatment that is more urgently required. This order will be resumed when the short-term treatment is complete.
   */
  SuspectedIntolerance: medicationrequest_status_reason_SuspectedIntolerance,
  /**
   * The drug is contraindicated for patients receiving surgery and the patient is scheduled to be admitted for surgery in the near future. The drug will be resumed when the patient has sufficiently recovered from the surgery.
   */
  PatientScheduledForSurgery: medicationrequest_status_reason_PatientScheduledForSurgery,
  /**
   * The patient was previously receiving a medication contraindicated with the current medication. The current medication will remain on hold until the prior medication has been cleansed from their system.
   */
  WaitingForOldDrugToWashOut: medicationrequest_status_reason_WaitingForOldDrugToWashOut,
};
const medication_statement_category_Community: Coding = {
  code: 'community',
  display: 'Community',
  system: 'http://terminology.hl7.org/CodeSystem/medication-statement-category',
};
const medication_statement_category_Inpatient: Coding = {
  code: 'inpatient',
  display: 'Inpatient',
  system: 'http://terminology.hl7.org/CodeSystem/medication-statement-category',
};
const medication_statement_category_Outpatient: Coding = {
  code: 'outpatient',
  display: 'Outpatient',
  system: 'http://terminology.hl7.org/CodeSystem/medication-statement-category',
};
const medication_statement_category_PatientSpecified: Coding = {
  code: 'patientspecified',
  display: 'Patient Specified',
  system: 'http://terminology.hl7.org/CodeSystem/medication-statement-category',
};
/**
 * Medication Status Codes
 */
export const MedicationStatementCategory = {
  /**
   * Includes orders for medications to be administered or consumed by the patient in their home (this would include long term care or nursing homes, hospices, etc.).
   */
  Community: medication_statement_category_Community,
  /**
   * Includes orders for medications to be administered or consumed in an inpatient or acute care setting
   */
  Inpatient: medication_statement_category_Inpatient,
  /**
   * Includes orders for medications to be administered or consumed in an outpatient setting (for example, Emergency Department, Outpatient Clinic, Outpatient Surgery, Doctor's office)
   */
  Outpatient: medication_statement_category_Outpatient,
  /**
   * Includes statements about medication use, including over the counter medication, provided by the patient, agent or another provider
   */
  PatientSpecified: medication_statement_category_PatientSpecified,
};
const medication_statement_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/CodeSystem/medication-statement-status',
};
const medication_statement_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/CodeSystem/medication-statement-status',
};
const medication_statement_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/CodeSystem/medication-statement-status',
};
const medication_statement_status_Intended: Coding = {
  code: 'intended',
  display: 'Intended',
  system: 'http://hl7.org/fhir/CodeSystem/medication-statement-status',
};
const medication_statement_status_NotTaken: Coding = {
  code: 'not-taken',
  display: 'Not Taken',
  system: 'http://hl7.org/fhir/CodeSystem/medication-statement-status',
};
const medication_statement_status_OnHold: Coding = {
  code: 'on-hold',
  display: 'On Hold',
  system: 'http://hl7.org/fhir/CodeSystem/medication-statement-status',
};
const medication_statement_status_Stopped: Coding = {
  code: 'stopped',
  display: 'Stopped',
  system: 'http://hl7.org/fhir/CodeSystem/medication-statement-status',
};
const medication_statement_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/CodeSystem/medication-statement-status',
};
/**
 * Medication Status Codes
 */
export const MedicationStatementStatus = {
  /**
   * The medication is still being taken.
   */
  Active: medication_statement_status_Active,
  /**
   * The medication is no longer being taken.
   */
  Completed: medication_statement_status_Completed,
  /**
   * Some of the actions that are implied by the medication statement may have occurred.  For example, the patient may have taken some of the medication.  Clinical decision support systems should take this status into account.
   */
  EnteredInError: medication_statement_status_EnteredInError,
  /**
   * The medication may be taken at some time in the future.
   */
  Intended: medication_statement_status_Intended,
  /**
   * The medication was not consumed by the patient
   */
  NotTaken: medication_statement_status_NotTaken,
  /**
   * Actions implied by the statement have been temporarily halted, but are expected to continue later. May also be called 'suspended'.
   */
  OnHold: medication_statement_status_OnHold,
  /**
   * Actions implied by the statement have been permanently halted, before all of them occurred. This should not be used if the statement was entered in error.
   */
  Stopped: medication_statement_status_Stopped,
  /**
   * The state of the medication use is not currently known.
   */
  Unknown: medication_statement_status_Unknown,
};
const medication_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/CodeSystem/medication-status',
};
const medication_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/CodeSystem/medication-status',
};
const medication_status_Inactive: Coding = {
  code: 'inactive',
  display: 'Inactive',
  system: 'http://hl7.org/fhir/CodeSystem/medication-status',
};
/**
 * Medication Status Codes
 */
export const MedicationStatus = {
  /**
   * The medication is available for use.
   */
  Active: medication_status_Active,
  /**
   * The medication was entered in error.
   */
  EnteredInError: medication_status_EnteredInError,
  /**
   * The medication is not available for use.
   */
  Inactive: medication_status_Inactive,
};
const messageheader_response_request_Always: Coding = {
  code: 'always',
  display: 'Always',
  system: 'http://hl7.org/fhir/messageheader-response-request',
};
const messageheader_response_request_Never: Coding = {
  code: 'never',
  display: 'Never',
  system: 'http://hl7.org/fhir/messageheader-response-request',
};
const messageheader_response_request_ErrorRejectConditionsOnly: Coding = {
  code: 'on-error',
  display: 'Error/reject conditions only',
  system: 'http://hl7.org/fhir/messageheader-response-request',
};
const messageheader_response_request_SuccessfulCompletionOnly: Coding = {
  code: 'on-success',
  display: 'Successful completion only',
  system: 'http://hl7.org/fhir/messageheader-response-request',
};
/**
 * HL7-defined table of codes which identify conditions under which acknowledgments are required to be returned in response to a message.
 */
export const MessageheaderResponseRequest = {
  /**
   * initiator expects a response for this message.
   */
  Always: messageheader_response_request_Always,
  /**
   * initiator does not expect a response.
   */
  Never: messageheader_response_request_Never,
  /**
   * initiator expects a response only if in error.
   */
  ErrorRejectConditionsOnly: messageheader_response_request_ErrorRejectConditionsOnly,
  /**
   * initiator expects a response only if successful.
   */
  SuccessfulCompletionOnly: messageheader_response_request_SuccessfulCompletionOnly,
};
const message_reason_encounter_Absent: Coding = {
  code: 'absent',
  display: 'Absent',
  system: 'http://terminology.hl7.org/CodeSystem/message-reasons-encounter',
};
const message_reason_encounter_Admit: Coding = {
  code: 'admit',
  display: 'Admit',
  system: 'http://terminology.hl7.org/CodeSystem/message-reasons-encounter',
};
const message_reason_encounter_Discharge: Coding = {
  code: 'discharge',
  display: 'Discharge',
  system: 'http://terminology.hl7.org/CodeSystem/message-reasons-encounter',
};
const message_reason_encounter_Edit: Coding = {
  code: 'edit',
  display: 'Edit',
  system: 'http://terminology.hl7.org/CodeSystem/message-reasons-encounter',
};
const message_reason_encounter_Moved: Coding = {
  code: 'moved',
  display: 'Moved',
  system: 'http://terminology.hl7.org/CodeSystem/message-reasons-encounter',
};
const message_reason_encounter_Returned: Coding = {
  code: 'return',
  display: 'Returned',
  system: 'http://terminology.hl7.org/CodeSystem/message-reasons-encounter',
};
/**
 * Example Message Reasons. These are the set of codes that might be used an updating an encounter using admin-update.
 */
export const MessageReasonEncounter = {
  /**
   * The patient has temporarily left the institution.
   */
  Absent: message_reason_encounter_Absent,
  /**
   * The patient has been admitted.
   */
  Admit: message_reason_encounter_Admit,
  /**
   * The patient has been discharged.
   */
  Discharge: message_reason_encounter_Discharge,
  /**
   * Encounter details have been updated (e.g. to correct a coding error).
   */
  Edit: message_reason_encounter_Edit,
  /**
   * The patient has been moved to a new location.
   */
  Moved: message_reason_encounter_Moved,
  /**
   * The patient has returned from a temporary absence.
   */
  Returned: message_reason_encounter_Returned,
};
const message_significance_category_Consequence: Coding = {
  code: 'consequence',
  display: 'Consequence',
  system: 'http://hl7.org/fhir/message-significance-category',
};
const message_significance_category_Currency: Coding = {
  code: 'currency',
  display: 'Currency',
  system: 'http://hl7.org/fhir/message-significance-category',
};
const message_significance_category_Notification: Coding = {
  code: 'notification',
  display: 'Notification',
  system: 'http://hl7.org/fhir/message-significance-category',
};
/**
 * The impact of the content of a message.
 */
export const MessageSignificanceCategory = {
  /**
   * The message represents/requests a change that should not be processed more than once; e.g., making a booking for an appointment.
   */
  Consequence: message_significance_category_Consequence,
  /**
   * The message represents a response to query for current information. Retrospective processing is wrong and/or wasteful.
   */
  Currency: message_significance_category_Currency,
  /**
   * The content is not necessarily intended to be current, and it can be reprocessed, though there may be version issues created by processing old notifications.
   */
  Notification: message_significance_category_Notification,
};
const message_transport_FTP: Coding = {
  code: 'ftp',
  display: 'FTP',
  system: 'http://terminology.hl7.org/CodeSystem/message-transport',
};
const message_transport_HTTP: Coding = {
  code: 'http',
  display: 'HTTP',
  system: 'http://terminology.hl7.org/CodeSystem/message-transport',
};
const message_transport_MLLP: Coding = {
  code: 'mllp',
  display: 'MLLP',
  system: 'http://terminology.hl7.org/CodeSystem/message-transport',
};
/**
 * The protocol used for message transport.
 */
export const MessageTransport = {
  /**
   * The application sends or receives messages using File Transfer Protocol.
   */
  FTP: message_transport_FTP,
  /**
   * The application sends or receives messages using HTTP POST (may be over http: or https:).
   */
  HTTP: message_transport_HTTP,
  /**
   * The application sends or receives messages using HL7's Minimal Lower Level Protocol.
   */
  MLLP: message_transport_MLLP,
};
const metric_calibration_state_Calibrated: Coding = {
  code: 'calibrated',
  display: 'Calibrated',
  system: 'http://hl7.org/fhir/metric-calibration-state',
};
const metric_calibration_state_CalibrationRequired: Coding = {
  code: 'calibration-required',
  display: 'Calibration Required',
  system: 'http://hl7.org/fhir/metric-calibration-state',
};
const metric_calibration_state_NotCalibrated: Coding = {
  code: 'not-calibrated',
  display: 'Not Calibrated',
  system: 'http://hl7.org/fhir/metric-calibration-state',
};
const metric_calibration_state_Unspecified: Coding = {
  code: 'unspecified',
  display: 'Unspecified',
  system: 'http://hl7.org/fhir/metric-calibration-state',
};
/**
 * Describes the state of a metric calibration.
 */
export const MetricCalibrationState = {
  /**
   * The metric has been calibrated.
   */
  Calibrated: metric_calibration_state_Calibrated,
  /**
   * The metric needs to be calibrated.
   */
  CalibrationRequired: metric_calibration_state_CalibrationRequired,
  /**
   * The metric has not been calibrated.
   */
  NotCalibrated: metric_calibration_state_NotCalibrated,
  /**
   * The state of calibration of this metric is unspecified.
   */
  Unspecified: metric_calibration_state_Unspecified,
};
const metric_calibration_type_Gain: Coding = {
  code: 'gain',
  display: 'Gain',
  system: 'http://hl7.org/fhir/metric-calibration-type',
};
const metric_calibration_type_Offset: Coding = {
  code: 'offset',
  display: 'Offset',
  system: 'http://hl7.org/fhir/metric-calibration-type',
};
const metric_calibration_type_TwoPoint: Coding = {
  code: 'two-point',
  display: 'Two Point',
  system: 'http://hl7.org/fhir/metric-calibration-type',
};
const metric_calibration_type_Unspecified: Coding = {
  code: 'unspecified',
  display: 'Unspecified',
  system: 'http://hl7.org/fhir/metric-calibration-type',
};
/**
 * Describes the type of a metric calibration.
 */
export const MetricCalibrationType = {
  /**
   * Gain metric calibration method.
   */
  Gain: metric_calibration_type_Gain,
  /**
   * Offset metric calibration method.
   */
  Offset: metric_calibration_type_Offset,
  /**
   * Two-point metric calibration method.
   */
  TwoPoint: metric_calibration_type_TwoPoint,
  /**
   * Metric calibration method has not been identified.
   */
  Unspecified: metric_calibration_type_Unspecified,
};
const metric_category_Calculation: Coding = {
  code: 'calculation',
  display: 'Calculation',
  system: 'http://hl7.org/fhir/metric-category',
};
const metric_category_Measurement: Coding = {
  code: 'measurement',
  display: 'Measurement',
  system: 'http://hl7.org/fhir/metric-category',
};
const metric_category_Setting: Coding = {
  code: 'setting',
  display: 'Setting',
  system: 'http://hl7.org/fhir/metric-category',
};
const metric_category_Unspecified: Coding = {
  code: 'unspecified',
  display: 'Unspecified',
  system: 'http://hl7.org/fhir/metric-category',
};
/**
 * Describes the category of the metric.
 */
export const MetricCategory = {
  /**
   * DeviceObservations generated for this DeviceMetric are calculated.
   */
  Calculation: metric_category_Calculation,
  /**
   * DeviceObservations generated for this DeviceMetric are measured.
   */
  Measurement: metric_category_Measurement,
  /**
   * DeviceObservations generated for this DeviceMetric is a setting that will influence the behavior of the Device.
   */
  Setting: metric_category_Setting,
  /**
   * The category of this DeviceMetric is unspecified.
   */
  Unspecified: metric_category_Unspecified,
};
const metric_color_ColorBlack: Coding = {
  code: 'black',
  display: 'Color Black',
  system: 'http://hl7.org/fhir/metric-color',
};
const metric_color_ColorBlue: Coding = {
  code: 'blue',
  display: 'Color Blue',
  system: 'http://hl7.org/fhir/metric-color',
};
const metric_color_ColorCyan: Coding = {
  code: 'cyan',
  display: 'Color Cyan',
  system: 'http://hl7.org/fhir/metric-color',
};
const metric_color_ColorGreen: Coding = {
  code: 'green',
  display: 'Color Green',
  system: 'http://hl7.org/fhir/metric-color',
};
const metric_color_ColorMagenta: Coding = {
  code: 'magenta',
  display: 'Color Magenta',
  system: 'http://hl7.org/fhir/metric-color',
};
const metric_color_ColorRed: Coding = {
  code: 'red',
  display: 'Color Red',
  system: 'http://hl7.org/fhir/metric-color',
};
const metric_color_ColorWhite: Coding = {
  code: 'white',
  display: 'Color White',
  system: 'http://hl7.org/fhir/metric-color',
};
const metric_color_ColorYellow: Coding = {
  code: 'yellow',
  display: 'Color Yellow',
  system: 'http://hl7.org/fhir/metric-color',
};
/**
 * Describes the typical color of representation.
 */
export const MetricColor = {
  /**
   * Color for representation - black.
   */
  ColorBlack: metric_color_ColorBlack,
  /**
   * Color for representation - blue.
   */
  ColorBlue: metric_color_ColorBlue,
  /**
   * Color for representation - cyan.
   */
  ColorCyan: metric_color_ColorCyan,
  /**
   * Color for representation - green.
   */
  ColorGreen: metric_color_ColorGreen,
  /**
   * Color for representation - magenta.
   */
  ColorMagenta: metric_color_ColorMagenta,
  /**
   * Color for representation - red.
   */
  ColorRed: metric_color_ColorRed,
  /**
   * Color for representation - white.
   */
  ColorWhite: metric_color_ColorWhite,
  /**
   * Color for representation - yellow.
   */
  ColorYellow: metric_color_ColorYellow,
};
const metric_operational_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered In Error',
  system: 'http://hl7.org/fhir/metric-operational-status',
};
const metric_operational_status_Off: Coding = {
  code: 'off',
  display: 'Off',
  system: 'http://hl7.org/fhir/metric-operational-status',
};
const metric_operational_status_On: Coding = {
  code: 'on',
  display: 'On',
  system: 'http://hl7.org/fhir/metric-operational-status',
};
const metric_operational_status_Standby: Coding = {
  code: 'standby',
  display: 'Standby',
  system: 'http://hl7.org/fhir/metric-operational-status',
};
/**
 * Describes the operational status of the DeviceMetric.
 */
export const MetricOperationalStatus = {
  /**
   * The DeviceMetric was entered in error.
   */
  EnteredInError: metric_operational_status_EnteredInError,
  /**
   * The DeviceMetric is not operating.
   */
  Off: metric_operational_status_Off,
  /**
   * The DeviceMetric is operating and will generate DeviceObservations.
   */
  On: metric_operational_status_On,
  /**
   * The DeviceMetric is operating, but will not generate any DeviceObservations.
   */
  Standby: metric_operational_status_Standby,
};
const missing_tooth_reason_C: Coding = {
  code: 'c',
  display: 'C',
  system: 'http://terminology.hl7.org/CodeSystem/missingtoothreason',
};
const missing_tooth_reason_E: Coding = {
  code: 'e',
  display: 'E',
  system: 'http://terminology.hl7.org/CodeSystem/missingtoothreason',
};
const missing_tooth_reason_O: Coding = {
  code: 'o',
  display: 'O',
  system: 'http://terminology.hl7.org/CodeSystem/missingtoothreason',
};
const missing_tooth_reason_U: Coding = {
  code: 'u',
  display: 'U',
  system: 'http://terminology.hl7.org/CodeSystem/missingtoothreason',
};
/**
 * This value set includes sample Missing Tooth Reason codes.
 */
export const MissingToothReason = {
  /**
   * Congenital
   */
  C: missing_tooth_reason_C,
  /**
   * Extraction
   */
  E: missing_tooth_reason_E,
  /**
   * Other
   */
  O: missing_tooth_reason_O,
  /**
   * Unknown
   */
  U: missing_tooth_reason_U,
};
const ModifiedFoodtype_Eggs: Coding = {
  code: '102263004',
  display: 'Eggs',
  system: 'http://snomed.info/sct',
};
const ModifiedFoodtype_StarchyFood: Coding = {
  code: '226529007',
  display: 'Starchy food',
  system: 'http://snomed.info/sct',
};
const ModifiedFoodtype_DairyFoods: Coding = {
  code: '226760005',
  display: 'Dairy foods',
  system: 'http://snomed.info/sct',
};
const ModifiedFoodtype_DietaryFatsAndOils: Coding = {
  code: '226887002',
  display: 'Dietary Fats and Oils',
  system: 'http://snomed.info/sct',
};
const ModifiedFoodtype_VegetablesPlusHerbsAndSpices: Coding = {
  code: '227210005',
  display: 'Vegetables plus herbs and spices',
  system: 'http://snomed.info/sct',
};
const ModifiedFoodtype_FruitNutsAndSeeds: Coding = {
  code: '227415002',
  display: 'Fruit Nuts and Seeds',
  system: 'http://snomed.info/sct',
};
const ModifiedFoodtype_SauceSeasoningsAndSoups: Coding = {
  code: '227518002',
  display: 'Sauce seasonings and soups',
  system: 'http://snomed.info/sct',
};
const ModifiedFoodtype_Vegetables: Coding = {
  code: '22836000',
  display: 'Vegetables',
  system: 'http://snomed.info/sct',
};
const ModifiedFoodtype_Foods: Coding = {
  code: '255620007',
  display: 'Foods',
  system: 'http://snomed.info/sct',
};
const ModifiedFoodtype_Grain: Coding = {
  code: '264331002',
  display: 'Grain',
  system: 'http://snomed.info/sct',
};
const ModifiedFoodtype_Meat: Coding = {
  code: '28647000',
  display: 'Meat',
  system: 'http://snomed.info/sct',
};
const ModifiedFoodtype_Seafood: Coding = {
  code: '44027008',
  display: 'Seafood',
  system: 'http://snomed.info/sct',
};
const ModifiedFoodtype_Fruit: Coding = {
  code: '72511004',
  display: 'Fruit',
  system: 'http://snomed.info/sct',
};
const ModifiedFoodtype_FoodStarch: Coding = {
  code: '74242007',
  display: 'Food Starch',
  system: 'http://snomed.info/sct',
};
/**
 * TextureModifiedFoodType: Codes for types of foods that are texture-modified. This value set is composed SNOMED CT Concepts from SCTID 255620007 Foods (substance) and is provided as a suggestive example.
 */
export const ModifiedFoodtype = {
  Eggs: ModifiedFoodtype_Eggs,
  StarchyFood: ModifiedFoodtype_StarchyFood,
  DairyFoods: ModifiedFoodtype_DairyFoods,
  DietaryFatsAndOils: ModifiedFoodtype_DietaryFatsAndOils,
  VegetablesPlusHerbsAndSpices: ModifiedFoodtype_VegetablesPlusHerbsAndSpices,
  FruitNutsAndSeeds: ModifiedFoodtype_FruitNutsAndSeeds,
  SauceSeasoningsAndSoups: ModifiedFoodtype_SauceSeasoningsAndSoups,
  Vegetables: ModifiedFoodtype_Vegetables,
  Foods: ModifiedFoodtype_Foods,
  Grain: ModifiedFoodtype_Grain,
  Meat: ModifiedFoodtype_Meat,
  Seafood: ModifiedFoodtype_Seafood,
  Fruit: ModifiedFoodtype_Fruit,
  FoodStarch: ModifiedFoodtype_FoodStarch,
};
const v2_0444_PrefixFamilyMiddleGivenSuffix: Coding = {
  code: 'F',
  display: 'Prefix Family Middle Given Suffix',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0444',
};
const v2_0444_PrefixGivenMiddleFamilySuffix: Coding = {
  code: 'G',
  display: 'Prefix Given Middle Family Suffix',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0444',
};
const name_assembly_order_OwnName: Coding = {
  code: 'NL1',
  display: 'Own Name',
  system: 'http://terminology.hl7.org/CodeSystem/name-assembly-order',
};
const name_assembly_order_PartnerName: Coding = {
  code: 'NL2',
  display: 'Partner Name',
  system: 'http://terminology.hl7.org/CodeSystem/name-assembly-order',
};
const name_assembly_order_PartnerNameFollowedByMaidenName: Coding = {
  code: 'NL3',
  display: 'Partner Name followed by Maiden Name',
  system: 'http://terminology.hl7.org/CodeSystem/name-assembly-order',
};
const name_assembly_order_OwnNameFollowedByPartnerName: Coding = {
  code: 'NL4',
  display: 'Own Name followed by Partner Name',
  system: 'http://terminology.hl7.org/CodeSystem/name-assembly-order',
};
/**
 * A code that represents the preferred display order of the components of a human name.
 */
export const NameAssemblyOrder = {
  PrefixFamilyMiddleGivenSuffix_v2_0444: v2_0444_PrefixFamilyMiddleGivenSuffix,
  PrefixGivenMiddleFamilySuffix_v2_0444: v2_0444_PrefixGivenMiddleFamilySuffix,
  OwnName_name_assembly_order: name_assembly_order_OwnName,
  PartnerName_name_assembly_order: name_assembly_order_PartnerName,
  PartnerNameFollowedByMaidenName_name_assembly_order: name_assembly_order_PartnerNameFollowedByMaidenName,
  OwnNameFollowedByPartnerName_name_assembly_order: name_assembly_order_OwnNameFollowedByPartnerName,
  /**
   * Description:A proper value is applicable, but not known.
   *
   *
   *                            Usage Notes: This means the actual value is not known.  If the only thing that is unknown is how to properly express the value in the necessary constraints (value set, datatype, etc.), then the OTH or UNC flavor should be used.  No properties should be included for a datatype with this property unless:
   *
   *
   *                            Those properties themselves directly translate to a semantic of "unknown".  (E.g. a local code sent as a translation that conveys 'unknown')
   *                            Those properties further qualify the nature of what is unknown.  (E.g. specifying a use code of "H" and a URL prefix of "tel:" to convey that it is the home phone number that is unknown.)
   */
  Unknown_v3_NullFlavor: v3_NullFlavor_Unknown,
};
const v3_EntityNamePartQualifierR2_Academic: Coding = {
  code: 'AC',
  display: 'academic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_Acquired: Coding = {
  code: 'AD',
  display: 'acquired',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_Birth: Coding = {
  code: 'BR',
  display: 'birth',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_Callme: Coding = {
  code: 'CL',
  display: 'callme',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_Honorific: Coding = {
  code: 'HON',
  display: 'honorific',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_Initial: Coding = {
  code: 'IN',
  display: 'initial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_LegalStatus: Coding = {
  code: 'LS',
  display: 'legal status',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_MiddleName: Coding = {
  code: 'MID',
  display: 'middle name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_Nobility: Coding = {
  code: 'NB',
  display: 'nobility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_Professional: Coding = {
  code: 'PR',
  display: 'professional',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_Spouse: Coding = {
  code: 'SP',
  display: 'spouse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifier_Voorvoegsel: Coding = {
  code: 'VV',
  display: 'voorvoegsel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
/**
 * A set of codes each of which specifies a certain subcategory of the name part in addition to the main name part type.
 */
export const NamePartQualifier = {
  /**
   * Description:Indicates that a title like "Dr.", "M.D." or "Ph.D." is an academic title.
   */
  Academic_v3_EntityNamePartQualifierR2: v3_EntityNamePartQualifierR2_Academic,
  /**
   * Description:A name part a person acquired.  The name part may be acquired by adoption, or the person may have chosen to use the name part for some other reason.
   *
   *
   *                            Note: this differs from an Other/Psuedonym/Alias in that an acquired name part is acquired on a formal basis rather than an informal one (e.g. registered as part of the official name).
   */
  Acquired_v3_EntityNamePartQualifierR2: v3_EntityNamePartQualifierR2_Acquired,
  /**
   * Description:A name that a person was given at birth or established as a consequence of adoption.
   *
   *
   *                            Note: This is not used for temporary names assigned at birth such as "Baby of Smith" a" which is just a name with a use code of "TEMP".
   */
  Birth_v3_EntityNamePartQualifierR2: v3_EntityNamePartQualifierR2_Birth,
  /**
   * Description:Used to indicate which of the various name parts is used when interacting with the person.
   */
  Callme_v3_EntityNamePartQualifierR2: v3_EntityNamePartQualifierR2_Callme,
  /**
   * Description:A honorific such as "The Right Honourable" or "Weledelgeleerde Heer".
   */
  Honorific_v3_EntityNamePartQualifierR2: v3_EntityNamePartQualifierR2_Honorific,
  /**
   * Description:Indicates that a name part is just an initial.  Initials do not imply a trailing period since this would not work with non-Latin scripts.  In some languages, initials may consist of more than one letter, e.g., "Ph" could stand for "Philippe" or "Th" For "Thomas".
   */
  Initial_v3_EntityNamePartQualifierR2: v3_EntityNamePartQualifierR2_Initial,
  /**
   * Description:For organizations a suffix indicating the legal status, e.g., "Inc.", "Co.", "AG", "GmbH", "B.V." "S.A.", "Ltd." etc.
   */
  LegalStatus_v3_EntityNamePartQualifierR2: v3_EntityNamePartQualifierR2_LegalStatus,
  /**
   * Description:Indicates that the name part is a middle name.
   *
   *
   *                            Usage Notes: In general, the english "middle name" concept is all of the given names after the first. This qualifier may be used to explicitly indicate which given names are considered to be middle names. The middle name qualifier may also be used with family names. This is a Scandinavian use case, matching the concept of "mellomnavn","mellannamn". Note that there are specific rules that indicate what names may be taken as a mellannamn in different Scandinavian countries.
   */
  MiddleName_v3_EntityNamePartQualifierR2: v3_EntityNamePartQualifierR2_MiddleName,
  /**
   * Description:A nobility title such as Sir, Count, Grafin.
   */
  Nobility_v3_EntityNamePartQualifierR2: v3_EntityNamePartQualifierR2_Nobility,
  /**
   * Description:Primarily in the British Imperial culture people tend to have an abbreviation of their professional organization as part of their credential titles.
   */
  Professional_v3_EntityNamePartQualifierR2: v3_EntityNamePartQualifierR2_Professional,
  /**
   * Description:The name assumed from the partner in a marital relationship.  Usually the spouse's family name.  Note that no inference about gender can be made from the existence of spouse names.
   */
  Spouse_v3_EntityNamePartQualifierR2: v3_EntityNamePartQualifierR2_Spouse,
  /**
   * A Dutch "voorvoegsel" is something like "van" or "de" that might have indicated nobility in the past but no longer so. Similar prefixes exist in other languages such as Spanish, French or Portugese.
   */
  Voorvoegsel_v3_EntityNamePartQualifier: v3_EntityNamePartQualifier_Voorvoegsel,
};
const name_use_Anonymous: Coding = {
  code: 'anonymous',
  display: 'Anonymous',
  system: 'http://hl7.org/fhir/name-use',
};
const name_use_NameChangedForMarriage: Coding = {
  code: 'maiden',
  display: 'Name changed for Marriage',
  system: 'http://hl7.org/fhir/name-use',
};
const name_use_Nickname: Coding = {
  code: 'nickname',
  display: 'Nickname',
  system: 'http://hl7.org/fhir/name-use',
};
const name_use_Official: Coding = {
  code: 'official',
  display: 'Official',
  system: 'http://hl7.org/fhir/name-use',
};
const name_use_Old: Coding = {
  code: 'old',
  display: 'Old',
  system: 'http://hl7.org/fhir/name-use',
};
const name_use_Temp: Coding = {
  code: 'temp',
  display: 'Temp',
  system: 'http://hl7.org/fhir/name-use',
};
const name_use_Usual: Coding = {
  code: 'usual',
  display: 'Usual',
  system: 'http://hl7.org/fhir/name-use',
};
/**
 * The use of a human name.
 */
export const NameUse = {
  /**
   * Anonymous assigned name, alias, or pseudonym (used to protect a person's identity for privacy reasons).
   */
  Anonymous: name_use_Anonymous,
  /**
   * A name used prior to changing name because of marriage. This name use is for use by applications that collect and store names that were used prior to a marriage. Marriage naming customs vary greatly around the world, and are constantly changing. This term is not gender specific. The use of this term does not imply any particular history for a person's name.
   */
  NameChangedForMarriage: name_use_NameChangedForMarriage,
  /**
   * A name that is used to address the person in an informal manner, but is not part of their formal or usual name.
   */
  Nickname: name_use_Nickname,
  /**
   * The formal name as registered in an official (government) registry, but which name might not be commonly used. May be called "legal name".
   */
  Official: name_use_Official,
  /**
   * This name is no longer in use (or was never correct, but retained for records).
   */
  Old: name_use_Old,
  /**
   * A temporary name. Name.period can provide more detailed information. This may also be used for temporary names assigned at birth or in emergency situations.
   */
  Temp: name_use_Temp,
  /**
   * Known as/conventional/the one you normally use.
   */
  Usual: name_use_Usual,
};
const v3_EntityNameUse_Alphabetic: Coding = {
  code: 'ABC',
  display: 'Alphabetic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
const v3_EntityNameUse_Ideographic: Coding = {
  code: 'IDE',
  display: 'Ideographic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
const v3_EntityNameUse_Syllabic: Coding = {
  code: 'SYL',
  display: 'Syllabic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
/**
 * A set of codes for each different representation of a name.
 */
export const NameV3Representation = {
  /**
   * Alphabetic transcription of name (Japanese: romaji)
   */
  Alphabetic: v3_EntityNameUse_Alphabetic,
  /**
   * Ideographic representation of name (e.g., Japanese kanji, Chinese characters)
   */
  Ideographic: v3_EntityNameUse_Ideographic,
  /**
   * Syllabic transcription of name (e.g., Japanese kana, Korean hangul)
   */
  Syllabic: v3_EntityNameUse_Syllabic,
};
const namingsystem_identifier_type_OID: Coding = {
  code: 'oid',
  display: 'OID',
  system: 'http://hl7.org/fhir/namingsystem-identifier-type',
};
const namingsystem_identifier_type_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://hl7.org/fhir/namingsystem-identifier-type',
};
const namingsystem_identifier_type_URI: Coding = {
  code: 'uri',
  display: 'URI',
  system: 'http://hl7.org/fhir/namingsystem-identifier-type',
};
const namingsystem_identifier_type_UUID: Coding = {
  code: 'uuid',
  display: 'UUID',
  system: 'http://hl7.org/fhir/namingsystem-identifier-type',
};
/**
 * Identifies the style of unique identifier used to identify a namespace.
 */
export const NamingsystemIdentifierType = {
  /**
   * An ISO object identifier; e.g. 1.2.3.4.5.
   */
  OID: namingsystem_identifier_type_OID,
  /**
   * Some other type of unique identifier; e.g. HL7-assigned reserved string such as LN for LOINC.
   */
  Other: namingsystem_identifier_type_Other,
  /**
   * A uniform resource identifier (ideally a URL - uniform resource locator); e.g. http://unitsofmeasure.org.
   */
  URI: namingsystem_identifier_type_URI,
  /**
   * A universally unique identifier of the form a5afddf4-e880-459b-876e-e4591b0acc11.
   */
  UUID: namingsystem_identifier_type_UUID,
};
const namingsystem_type_CodeSystem: Coding = {
  code: 'codesystem',
  display: 'Code System',
  system: 'http://hl7.org/fhir/namingsystem-type',
};
const namingsystem_type_Identifier: Coding = {
  code: 'identifier',
  display: 'Identifier',
  system: 'http://hl7.org/fhir/namingsystem-type',
};
const namingsystem_type_Root: Coding = {
  code: 'root',
  display: 'Root',
  system: 'http://hl7.org/fhir/namingsystem-type',
};
/**
 * Identifies the purpose of the naming system.
 */
export const NamingsystemType = {
  /**
   * The naming system is used to define concepts and symbols to represent those concepts; e.g. UCUM, LOINC, NDC code, local lab codes, etc.
   */
  CodeSystem: namingsystem_type_CodeSystem,
  /**
   * The naming system is used to manage identifiers (e.g. license numbers, order numbers, etc.).
   */
  Identifier: namingsystem_type_Identifier,
  /**
   * The naming system is used as the root for other identifiers and naming systems.
   */
  Root: namingsystem_type_Root,
};
const narrative_status_Additional: Coding = {
  code: 'additional',
  display: 'Additional',
  system: 'http://hl7.org/fhir/narrative-status',
};
const narrative_status_Empty: Coding = {
  code: 'empty',
  display: 'Empty',
  system: 'http://hl7.org/fhir/narrative-status',
};
const narrative_status_Extensions: Coding = {
  code: 'extensions',
  display: 'Extensions',
  system: 'http://hl7.org/fhir/narrative-status',
};
const narrative_status_Generated: Coding = {
  code: 'generated',
  display: 'Generated',
  system: 'http://hl7.org/fhir/narrative-status',
};
/**
 * The status of a resource narrative.
 */
export const NarrativeStatus = {
  /**
   * The contents of the narrative may contain additional information not found in the structured data. Note that there is no computable way to determine what the extra information is, other than by human inspection.
   */
  Additional: narrative_status_Additional,
  /**
   * The contents of the narrative are some equivalent of "No human-readable text provided in this case".
   */
  Empty: narrative_status_Empty,
  /**
   * The contents of the narrative are entirely generated from the core elements in the content and some of the content is generated from extensions. The narrative SHALL reflect the impact of all modifier extensions.
   */
  Extensions: narrative_status_Extensions,
  /**
   * The contents of the narrative are entirely generated from the core elements in the content.
   */
  Generated: narrative_status_Generated,
};
const network_type_MachineName: Coding = {
  code: '1',
  display: 'Machine Name',
  system: 'http://hl7.org/fhir/network-type',
};
const network_type_IPAddress: Coding = {
  code: '2',
  display: 'IP Address',
  system: 'http://hl7.org/fhir/network-type',
};
const network_type_TelephoneNumber: Coding = {
  code: '3',
  display: 'Telephone Number',
  system: 'http://hl7.org/fhir/network-type',
};
const network_type_EmailAddress: Coding = {
  code: '4',
  display: 'Email address',
  system: 'http://hl7.org/fhir/network-type',
};
const network_type_URI: Coding = {
  code: '5',
  display: 'URI',
  system: 'http://hl7.org/fhir/network-type',
};
/**
 * The type of network access point of this agent in the audit event.
 */
export const NetworkType = {
  /**
   * The machine name, including DNS name.
   */
  MachineName: network_type_MachineName,
  /**
   * The assigned Internet Protocol (IP) address.
   */
  IPAddress: network_type_IPAddress,
  /**
   * The assigned telephone number.
   */
  TelephoneNumber: network_type_TelephoneNumber,
  /**
   * The assigned email address.
   */
  EmailAddress: network_type_EmailAddress,
  /**
   * URI (User directory, HTTP-PUT, ftp, etc.).
   */
  URI: network_type_URI,
};
const nhin_purposeofuse_Abuse: Coding = {
  code: 'ABUSE',
  display: 'Abuse',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Coverage: Coding = {
  code: 'COVERAGE',
  display: 'Coverage',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Deceased: Coding = {
  code: 'DECEASED',
  display: 'Deceased',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Directory: Coding = {
  code: 'DIRECTORY',
  display: 'Directory',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Disaster: Coding = {
  code: 'DISASTER',
  display: 'Disaster',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Donation: Coding = {
  code: 'DONATION',
  display: 'Donation',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Emergency: Coding = {
  code: 'EMERGENCY',
  display: 'Emergency',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Family: Coding = {
  code: 'FAMILY',
  display: 'Family',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Fraud: Coding = {
  code: 'FRAUD',
  display: 'Fraud',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Government: Coding = {
  code: 'GOVERNMENT',
  display: 'Government',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Judicial: Coding = {
  code: 'JUDICIAL',
  display: 'Judicial',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_LawEnforcement: Coding = {
  code: 'LAW',
  display: 'Law Enforcement',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Legal: Coding = {
  code: 'LEGAL',
  display: 'Legal',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Marketing: Coding = {
  code: 'MARKETING',
  display: 'Marketing',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Operations: Coding = {
  code: 'OPERATIONS',
  display: 'Operations',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Oversight: Coding = {
  code: 'OVERSIGHT',
  display: 'Oversight',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Payment: Coding = {
  code: 'PAYMENT',
  display: 'Payment',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Present: Coding = {
  code: 'PRESENT',
  display: 'Present',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Psychotherapy: Coding = {
  code: 'PSYCHOTHERAPY',
  display: 'Psychotherapy',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_PublicHealth: Coding = {
  code: 'PUBLICHEALTH',
  display: 'Public Health',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Request: Coding = {
  code: 'REQUEST',
  display: 'Request',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Research: Coding = {
  code: 'RESEARCH',
  display: 'Research',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Sysadmin: Coding = {
  code: 'SYSADMIN',
  display: 'Sysadmin',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Threat: Coding = {
  code: 'THREAT',
  display: 'Threat',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Training: Coding = {
  code: 'TRAINING',
  display: 'Training',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_Treatment: Coding = {
  code: 'TREATMENT',
  display: 'Treatment',
  system: 'http://healthit.gov/nhin/purposeofuse',
};
const nhin_purposeofuse_WorkerQuoteSComp: Coding = {
  code: 'WORKERSCOMP',
  display: "Worker's Comp",
  system: 'http://healthit.gov/nhin/purposeofuse',
};
/**
 * This value set is suitable for use with the provenance resource. It is derived from, but not compatible with, the HL7 v3 Purpose of use Code system.
 */
export const NhinPurposeofuse = {
  /**
   * Disclosures about victims of abuse, neglect or domestic violence.
   */
  Abuse: nhin_purposeofuse_Abuse,
  /**
   * Disclosures for insurance or disability coverage determination
   */
  Coverage: nhin_purposeofuse_Coverage,
  /**
   * Uses and disclosures about decedents.
   */
  Deceased: nhin_purposeofuse_Deceased,
  /**
   * Use and disclosure for facility directories
   */
  Directory: nhin_purposeofuse_Directory,
  /**
   * Use and disclosures for disaster relief purposes.
   */
  Disaster: nhin_purposeofuse_Disaster,
  /**
   * Uses and disclosures for cadaveric organ,  eye or tissue donation purposes
   */
  Donation: nhin_purposeofuse_Donation,
  /**
   * Permission cannot practicably be provided because of the individual's incapacity or an emergency.
   */
  Emergency: nhin_purposeofuse_Emergency,
  /**
   * Disclose to a family member, other relative, or a close personal friend of the individual
   */
  Family: nhin_purposeofuse_Family,
  /**
   * Fraud detection
   */
  Fraud: nhin_purposeofuse_Fraud,
  /**
   * Uses and disclosures for specialized government functions.
   */
  Government: nhin_purposeofuse_Government,
  /**
   * Disclosures for judicial and administrative proceedings.
   */
  Judicial: nhin_purposeofuse_Judicial,
  /**
   * Disclosures for law enforcement purposes.
   */
  LawEnforcement: nhin_purposeofuse_LawEnforcement,
  /**
   * Use or disclosure by the covered entity to defend itself in a legal action
   */
  Legal: nhin_purposeofuse_Legal,
  /**
   * Marketing
   */
  Marketing: nhin_purposeofuse_Marketing,
  /**
   * Healthcare Operations
   */
  Operations: nhin_purposeofuse_Operations,
  /**
   * Uses and disclosures for health oversight activities.
   */
  Oversight: nhin_purposeofuse_Oversight,
  /**
   * Payment
   */
  Payment: nhin_purposeofuse_Payment,
  /**
   * Uses and disclosures with the individual present.
   */
  Present: nhin_purposeofuse_Present,
  /**
   * Use or disclosure of Psychotherapy Notes
   */
  Psychotherapy: nhin_purposeofuse_Psychotherapy,
  /**
   * Uses and disclosures for public health activities.
   */
  PublicHealth: nhin_purposeofuse_PublicHealth,
  /**
   * Request of the Individual
   */
  Request: nhin_purposeofuse_Request,
  /**
   * Uses and disclosures for research purposes.
   */
  Research: nhin_purposeofuse_Research,
  /**
   * System Administration
   */
  Sysadmin: nhin_purposeofuse_Sysadmin,
  /**
   * Uses and disclosures to avert a serious threat to health or safety.
   */
  Threat: nhin_purposeofuse_Threat,
  /**
   * Use or disclosure by the covered entity for its own training programs
   */
  Training: nhin_purposeofuse_Training,
  /**
   * Treatment
   */
  Treatment: nhin_purposeofuse_Treatment,
  /**
   * Disclosures for workers' compensation.
   */
  WorkerQuoteSComp: nhin_purposeofuse_WorkerQuoteSComp,
};
const note_type_Display: Coding = {
  code: 'display',
  display: 'Display',
  system: 'http://hl7.org/fhir/note-type',
};
const note_type_PrintForm: Coding = {
  code: 'print',
  display: 'Print (Form)',
  system: 'http://hl7.org/fhir/note-type',
};
const note_type_PrintOperator: Coding = {
  code: 'printoper',
  display: 'Print (Operator)',
  system: 'http://hl7.org/fhir/note-type',
};
/**
 * The presentation types of notes.
 */
export const NoteType = {
  /**
   * Display the note.
   */
  Display: note_type_Display,
  /**
   * Print the note on the form.
   */
  PrintForm: note_type_PrintForm,
  /**
   * Print the note for the operator.
   */
  PrintOperator: note_type_PrintOperator,
};
const NutrientCode_Fluid: Coding = {
  code: '33463005',
  display: 'Fluid',
  system: 'http://snomed.info/sct',
};
const NutrientCode_Sodium: Coding = {
  code: '39972003',
  display: 'Sodium',
  system: 'http://snomed.info/sct',
};
const NutrientCode_Potassium: Coding = {
  code: '88480006',
  display: 'Potassium',
  system: 'http://snomed.info/sct',
};
/**
 * NutrientModifier :  Codes for types of nutrients that are being modified such as carbohydrate or sodium.  This value set includes codes from [SNOMED CT](http://snomed.info/sct) where concept is-a 226355009 (Nutrients(substance)), and the concepts for Sodium, Potassium and Fluid. This is provided as a suggestive example.
 */
export const NutrientCode = {
  Fluid: NutrientCode_Fluid,
  Sodium: NutrientCode_Sodium,
  Potassium: NutrientCode_Potassium,
};
const dicom_audit_lifecycle_OriginationCreation: Coding = {
  code: '1',
  display: 'Origination / Creation',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_Export: Coding = {
  code: '10',
  display: 'Export',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_Disclosure: Coding = {
  code: '11',
  display: 'Disclosure',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_ReceiptOfDisclosure: Coding = {
  code: '12',
  display: 'Receipt of disclosure',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_Archiving: Coding = {
  code: '13',
  display: 'Archiving',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_LogicalDeletion: Coding = {
  code: '14',
  display: 'Logical deletion',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_PermanentErasurePhysicalDestruction: Coding = {
  code: '15',
  display: 'Permanent erasure / Physical destruction',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_ImportCopy: Coding = {
  code: '2',
  display: 'Import / Copy',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_Amendment: Coding = {
  code: '3',
  display: 'Amendment',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_Verification: Coding = {
  code: '4',
  display: 'Verification',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_Translation: Coding = {
  code: '5',
  display: 'Translation',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_AccessUse: Coding = {
  code: '6',
  display: 'Access / Use',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_DeIdentification: Coding = {
  code: '7',
  display: 'De-identification',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_AggregationSummarizationDerivation: Coding = {
  code: '8',
  display: 'Aggregation / summarization / derivation',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
const dicom_audit_lifecycle_Report: Coding = {
  code: '9',
  display: 'Report',
  system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
};
/**
 * This example FHIR value set is comprised of lifecycle event codes. The FHIR Actor value set is based on    DICOM Audit Message, ParticipantObjectDataLifeCycle;   ISO Standard, TS 21089-2017;
 */
export const ObjectLifecycleEvents = {
  OriginationCreation_dicom_audit_lifecycle: dicom_audit_lifecycle_OriginationCreation,
  Export_dicom_audit_lifecycle: dicom_audit_lifecycle_Export,
  Disclosure_dicom_audit_lifecycle: dicom_audit_lifecycle_Disclosure,
  ReceiptOfDisclosure_dicom_audit_lifecycle: dicom_audit_lifecycle_ReceiptOfDisclosure,
  Archiving_dicom_audit_lifecycle: dicom_audit_lifecycle_Archiving,
  LogicalDeletion_dicom_audit_lifecycle: dicom_audit_lifecycle_LogicalDeletion,
  PermanentErasurePhysicalDestruction_dicom_audit_lifecycle: dicom_audit_lifecycle_PermanentErasurePhysicalDestruction,
  ImportCopy_dicom_audit_lifecycle: dicom_audit_lifecycle_ImportCopy,
  Amendment_dicom_audit_lifecycle: dicom_audit_lifecycle_Amendment,
  Verification_dicom_audit_lifecycle: dicom_audit_lifecycle_Verification,
  Translation_dicom_audit_lifecycle: dicom_audit_lifecycle_Translation,
  AccessUse_dicom_audit_lifecycle: dicom_audit_lifecycle_AccessUse,
  DeIdentification_dicom_audit_lifecycle: dicom_audit_lifecycle_DeIdentification,
  AggregationSummarizationDerivation_dicom_audit_lifecycle: dicom_audit_lifecycle_AggregationSummarizationDerivation,
  Report_dicom_audit_lifecycle: dicom_audit_lifecycle_Report,
  /**
   * Occurs when an agent causes the system to obtain and open a record entry for inspection or review.
   */
  AccessViewRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_AccessViewRecordLifecycleEvent,
  /**
   * Occurs when an agent makes any change to record entry content currently residing in storage considered permanent (persistent).
   */
  AmendUpdateRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_AmendUpdateRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to create and move archive artifacts containing record entry content, typically to long-term offline storage.
   */
  ArchiveRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_ArchiveRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to capture the agent’s digital signature (or equivalent indication) during formal validation of record entry content.
   */
  AttestRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_AttestRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to decode record entry content from a cipher.
   */
  DecryptRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_DecryptRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to scrub record entry content to reduce the association between a set of identifying data and the data subject in a way that might or might not be reversible.
   */
  DeIdentifyAnononymizeRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_DeIdentifyAnononymizeRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to tag record entry(ies) as obsolete, erroneous or untrustworthy, to warn against its future use.
   */
  DeprecateRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_DeprecateRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to permanently erase record entry content from the system.
   */
  DestroyDeleteRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_DestroyDeleteRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to release, transfer, provision access to, or otherwise divulge record entry content.
   */
  DiscloseRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_DiscloseRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to encode record entry content in a cipher.
   */
  EncryptRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_EncryptRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to selectively pull out a subset of record entry content, based on explicit criteria.
   */
  ExtractRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_ExtractRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to tag or otherwise indicate special access management and suspension of record entry deletion/destruction, if deemed relevant to a lawsuit or which are reasonably anticipated to be relevant or to fulfill organizational policy under the legal doctrine of “duty to preserve”.
   */
  AddLegalHoldRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_AddLegalHoldRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to connect related record entries.
   */
  LinkRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_LinkRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to combine or join content from two or more record entries, resulting in a single logical record entry.
   */
  MergeRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_MergeRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to: a) initiate capture of potential record content, and b) incorporate that content into the storage considered a permanent part of the health record.
   */
  OriginateRetainRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_OriginateRetainRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to remove record entry content to reduce the association between a set of identifying data and the data subject in a way that may be reversible.
   */
  PseudonymizeRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_PseudonymizeRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to recreate or restore full status to record entries previously deleted or deprecated.
   */
  ReActivateRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_ReActivateRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to a) initiate capture of data content from elsewhere, and b) incorporate that content into the storage considered a permanent part of the health record.
   */
  ReceiveRetainRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_ReceiveRetainRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to restore information to data that allows identification of information source and/or information subject.
   */
  ReIdentifyRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_ReIdentifyRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to produce and deliver record entry content in a particular form and manner.
   */
  ReportOutputRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_ReportOutputRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to recreate record entries and their content from a previous created archive artefact.
   */
  RestoreRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_RestoreRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to change the form, language or code system used to represent record entry content.
   */
  TransformTranslateRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_TransformTranslateRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to send record entry content from one (EHR/PHR/other) system to another.
   */
  TransmitRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_TransmitRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to remove a tag or other cues for special access management had required to fulfill organizational policy under the legal doctrine of “duty to preserve”.
   */
  RemoveLegalHoldRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_RemoveLegalHoldRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to disconnect two or more record entries previously connected, rendering them separate (disconnected) again.
   */
  UnlinkRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_UnlinkRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to reverse a previous record entry merge operation, rendering them separate again.
   */
  UnmergeRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_UnmergeRecordLifecycleEvent,
  /**
   * Occurs when an agent causes the system to confirm compliance of data or data objects with regulations, requirements, specifications, or other imposed conditions based on organizational policy.
   */
  VerifyRecordLifecycleEvent_iso_21089_lifecycle: iso_21089_lifecycle_VerifyRecordLifecycleEvent,
};
const object_role_Patient: Coding = {
  code: '1',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_Guarantor: Coding = {
  code: '10',
  display: 'Guarantor',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_SecurityUserEntity: Coding = {
  code: '11',
  display: 'Security User Entity',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_SecurityUserGroup: Coding = {
  code: '12',
  display: 'Security User Group',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_SecurityResource: Coding = {
  code: '13',
  display: 'Security Resource',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_SecurityGranularityDefinition: Coding = {
  code: '14',
  display: 'Security Granularity Definition',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_Practitioner: Coding = {
  code: '15',
  display: 'Practitioner',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_DataDestination: Coding = {
  code: '16',
  display: 'Data Destination',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_DataRepository: Coding = {
  code: '17',
  display: 'Data Repository',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_Schedule: Coding = {
  code: '18',
  display: 'Schedule',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_Customer: Coding = {
  code: '19',
  display: 'Customer',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_Location: Coding = {
  code: '2',
  display: 'Location',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_Job: Coding = {
  code: '20',
  display: 'Job',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_JobStream: Coding = {
  code: '21',
  display: 'Job Stream',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_Table: Coding = {
  code: '22',
  display: 'Table',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_RoutingCriteria: Coding = {
  code: '23',
  display: 'Routing Criteria',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_Query: Coding = {
  code: '24',
  display: 'Query',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_Report: Coding = {
  code: '3',
  display: 'Report',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_DomainResource: Coding = {
  code: '4',
  display: 'Domain Resource',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_MasterFile: Coding = {
  code: '5',
  display: 'Master file',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_User: Coding = {
  code: '6',
  display: 'User',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_List: Coding = {
  code: '7',
  display: 'List',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_Doctor: Coding = {
  code: '8',
  display: 'Doctor',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
const object_role_Subscriber: Coding = {
  code: '9',
  display: 'Subscriber',
  system: 'http://terminology.hl7.org/CodeSystem/object-role',
};
/**
 * Code representing the role the entity played in the audit event.
 */
export const ObjectRole = {
  /**
   * This object is the patient that is the subject of care related to this event.  It is identifiable by patient ID or equivalent.  The patient may be either human or animal.
   */
  Patient: object_role_Patient,
  /**
   * Insurance company, or any other organization who accepts responsibility for paying for the healthcare event.
   */
  Guarantor: object_role_Guarantor,
  /**
   * A person or active system object involved in the event with a security role.
   */
  SecurityUserEntity: object_role_SecurityUserEntity,
  /**
   * A person or system object involved in the event with the authority to modify security roles of other objects.
   */
  SecurityUserGroup: object_role_SecurityUserGroup,
  /**
   * A passive object, such as a role table, that is relevant to the event.
   */
  SecurityResource: object_role_SecurityResource,
  /**
   * (deprecated)  Relevant to certain RBAC security methodologies.
   */
  SecurityGranularityDefinition: object_role_SecurityGranularityDefinition,
  /**
   * Any person or organization responsible for providing care.  This encompasses all forms of care, licensed or otherwise, and all sorts of teams and care groups. Note the distinction between practitioner and the doctor that actually provided the care to the patient.
   */
  Practitioner: object_role_Practitioner,
  /**
   * The source or destination for data transfer, when it does not match some other role.
   */
  DataDestination: object_role_DataDestination,
  /**
   * A source or destination for data transfer that acts as an archive, database, or similar role.
   */
  DataRepository: object_role_DataRepository,
  /**
   * An object that holds schedule information.  This could be an appointment book, availability information, etc.
   */
  Schedule: object_role_Schedule,
  /**
   * An organization or person that is the recipient of services.  This could be an organization that is buying services for a patient, or a person that is buying services for an animal.
   */
  Customer: object_role_Customer,
  /**
   * This is a location identified as related to the event.  This is usually the location where the event took place.  Note that for shipping, the usual events are arrival at a location or departure from a location.
   */
  Location: object_role_Location,
  /**
   * An order, task, work item, procedure step, or other description of work to be performed; e.g. a particular instance of an MPPS.
   */
  Job: object_role_Job,
  /**
   * A list of jobs or a system that provides lists of jobs; e.g. an MWL SCP.
   */
  JobStream: object_role_JobStream,
  /**
   * (Deprecated).
   */
  Table: object_role_Table,
  /**
   * An object that specifies or controls the routing or delivery of items.  For example, a distribution list is the routing criteria for mail.  The items delivered may be documents, jobs, or other objects.
   */
  RoutingCriteria: object_role_RoutingCriteria,
  /**
   * The contents of a query.  This is used to capture the contents of any kind of query.  For security surveillance purposes knowing the queries being made is very important.
   */
  Query: object_role_Query,
  /**
   * This object is any kind of persistent document created as a result of the event.  This could be a paper report, film, electronic report, DICOM Study, etc.  Issues related to medical records life cycle management are conveyed elsewhere.
   */
  Report: object_role_Report,
  /**
   * A logical object related to a health record event.  This is any healthcare  specific resource (object) not restricted to FHIR defined Resources.
   */
  DomainResource: object_role_DomainResource,
  /**
   * This is any configurable file used to control creation of documents.  Examples include the objects maintained by the HL7 Master File transactions, Value Sets, etc.
   */
  MasterFile: object_role_MasterFile,
  /**
   * A human participant not otherwise identified by some other category.
   */
  User: object_role_User,
  /**
   * (deprecated).
   */
  List: object_role_List,
  /**
   * Typically, a licensed person who is providing or performing care related to the event, generally a physician.   The key distinction between doctor and practitioner is with regards to their role, not the licensing.  The doctor is the human who actually performed the work.  The practitioner is the human or organization that is responsible for the work.
   */
  Doctor: object_role_Doctor,
  /**
   * A person or system that is being notified as part of the event.  This is relevant in situations where automated systems provide notifications to other parties when an event took place.
   */
  Subscriber: object_role_Subscriber,
};
const observation_category_Activity: Coding = {
  code: 'activity',
  display: 'Activity',
  system: 'http://terminology.hl7.org/CodeSystem/observation-category',
};
const observation_category_Exam: Coding = {
  code: 'exam',
  display: 'Exam',
  system: 'http://terminology.hl7.org/CodeSystem/observation-category',
};
const observation_category_Imaging: Coding = {
  code: 'imaging',
  display: 'Imaging',
  system: 'http://terminology.hl7.org/CodeSystem/observation-category',
};
const observation_category_Laboratory: Coding = {
  code: 'laboratory',
  display: 'Laboratory',
  system: 'http://terminology.hl7.org/CodeSystem/observation-category',
};
const observation_category_Procedure: Coding = {
  code: 'procedure',
  display: 'Procedure',
  system: 'http://terminology.hl7.org/CodeSystem/observation-category',
};
const observation_category_SocialHistory: Coding = {
  code: 'social-history',
  display: 'Social History',
  system: 'http://terminology.hl7.org/CodeSystem/observation-category',
};
const observation_category_Survey: Coding = {
  code: 'survey',
  display: 'Survey',
  system: 'http://terminology.hl7.org/CodeSystem/observation-category',
};
const observation_category_Therapy: Coding = {
  code: 'therapy',
  display: 'Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/observation-category',
};
const observation_category_VitalSigns: Coding = {
  code: 'vital-signs',
  display: 'Vital Signs',
  system: 'http://terminology.hl7.org/CodeSystem/observation-category',
};
/**
 * Observation Category codes.
 */
export const ObservationCategory = {
  /**
   * Observations that measure or record any bodily activity that enhances or maintains physical fitness and overall health and wellness.  Not under direct supervision of practitioner such as a physical therapist. (e.g., laps swum, steps, sleep data)
   */
  Activity: observation_category_Activity,
  /**
   * Observations generated by physical exam findings including direct observations made by a clinician and use of simple instruments and the result of simple maneuvers performed directly on the patient's body.
   */
  Exam: observation_category_Exam,
  /**
   * Observations generated by imaging. The scope includes observations regarding plain x-ray, ultrasound, CT, MRI, angiography, echocardiography, and nuclear medicine.
   */
  Imaging: observation_category_Imaging,
  /**
   * The results of observations generated by laboratories.  Laboratory results are typically generated by laboratories providing analytic services in areas such as chemistry, hematology, serology, histology, cytology, anatomic pathology (including digital pathology), microbiology, and/or virology. These observations are based on analysis of specimens obtained from the patient and submitted to the laboratory.
   */
  Laboratory: observation_category_Laboratory,
  /**
   * Observations generated by other procedures.  This category includes observations resulting from interventional and non-interventional procedures excluding laboratory and imaging (e.g., cardiology catheterization, endoscopy, electrodiagnostics, etc.).  Procedure results are typically generated by a clinician to provide more granular information about component observations made during a procedure.  An example would be when a gastroenterologist reports the size of a polyp observed during a colonoscopy.
   */
  Procedure: observation_category_Procedure,
  /**
   * Social History Observations define the patient's occupational, personal (e.g., lifestyle), social, familial, and environmental history and health risk factors that may impact the patient's health.
   */
  SocialHistory: observation_category_SocialHistory,
  /**
   * Assessment tool/survey instrument observations (e.g., Apgar Scores, Montreal Cognitive Assessment (MoCA)).
   */
  Survey: observation_category_Survey,
  /**
   * Observations generated by non-interventional treatment protocols (e.g. occupational, physical, radiation, nutritional and medication therapy)
   */
  Therapy: observation_category_Therapy,
  /**
   *  Clinical observations measure the body's basic functions such as blood pressure, heart rate, respiratory rate, height, weight, body mass index, head circumference, pulse oximetry, temperature, and body surface area.
   */
  VitalSigns: observation_category_VitalSigns,
};
const v3_ObservationInterpretation_GeneticObservationInterpretation: Coding = {
  code: '_GeneticObservationInterpretation',
  display: 'GeneticObservationInterpretation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_ObservationInterpretationChange: Coding = {
  code: '_ObservationInterpretationChange',
  display: 'ObservationInterpretationChange',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_ObservationInterpretationExceptions: Coding = {
  code: '_ObservationInterpretationExceptions',
  display: 'ObservationInterpretationExceptions',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_ObservationInterpretationNormality: Coding = {
  code: '_ObservationInterpretationNormality',
  display: 'ObservationInterpretationNormality',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_ObservationInterpretationSusceptibility: Coding = {
  code: '_ObservationInterpretationSusceptibility',
  display: 'ObservationInterpretationSusceptibility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_OffScaleLow: Coding = {
  code: '<',
  display: 'Off scale low',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_OffScaleHigh: Coding = {
  code: '>',
  display: 'Off scale high',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Abnormal: Coding = {
  code: 'A',
  display: 'Abnormal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_CriticalAbnormal: Coding = {
  code: 'AA',
  display: 'Critical abnormal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Better: Coding = {
  code: 'B',
  display: 'Better',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Carrier: Coding = {
  code: 'CAR',
  display: 'Carrier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_SignificantChangeDown: Coding = {
  code: 'D',
  display: 'Significant change down',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Detected: Coding = {
  code: 'DET',
  display: 'Detected',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Equivocal: Coding = {
  code: 'E',
  display: 'Equivocal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_OutsideThreshold: Coding = {
  code: 'EX',
  display: 'outside threshold',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Expected: Coding = {
  code: 'EXP',
  display: 'Expected',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_High: Coding = {
  code: 'H',
  display: 'High',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_CriticalHigh: Coding = {
  code: 'HH',
  display: 'Critical high',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_SignificantlyHigh: Coding = {
  code: 'HU',
  display: 'Significantly high',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_AboveHighThreshold: Coding = {
  code: 'HX',
  display: 'above high threshold',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Intermediate: Coding = {
  code: 'I',
  display: 'Intermediate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_InsufficientEvidence: Coding = {
  code: 'IE',
  display: 'Insufficient evidence',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Indeterminate: Coding = {
  code: 'IND',
  display: 'Indeterminate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Low: Coding = {
  code: 'L',
  display: 'Low',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_CriticalLow: Coding = {
  code: 'LL',
  display: 'Critical low',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_SignificantlyLow: Coding = {
  code: 'LU',
  display: 'Significantly low',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_BelowLowThreshold: Coding = {
  code: 'LX',
  display: 'below low threshold',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Normal: Coding = {
  code: 'N',
  display: 'Normal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_NoCLSIDefinedBreakpoint: Coding = {
  code: 'NCL',
  display: 'No CLSI defined breakpoint',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_NotDetected: Coding = {
  code: 'ND',
  display: 'Not detected',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Negative: Coding = {
  code: 'NEG',
  display: 'Negative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_NonReactive: Coding = {
  code: 'NR',
  display: 'Non-reactive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_NonSusceptible: Coding = {
  code: 'NS',
  display: 'Non-susceptible',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_ObservationInterpretationDetection: Coding = {
  code: 'ObservationInterpretationDetection',
  display: 'ObservationInterpretationDetection',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_ObservationInterpretationExpectation: Coding = {
  code: 'ObservationInterpretationExpectation',
  display: 'ObservationInterpretationExpectation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Positive: Coding = {
  code: 'POS',
  display: 'Positive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Resistant: Coding = {
  code: 'R',
  display: 'Resistant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_ReactivityObservationInterpretation: Coding = {
  code: 'ReactivityObservationInterpretation',
  display: 'ReactivityObservationInterpretation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Reactive: Coding = {
  code: 'RR',
  display: 'Reactive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Susceptible: Coding = {
  code: 'S',
  display: 'Susceptible',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_SusceptibleDoseDependent: Coding = {
  code: 'SDD',
  display: 'Susceptible-dose dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_SynergyResistant: Coding = {
  code: 'SYN-R',
  display: 'Synergy - resistant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_SynergySusceptible: Coding = {
  code: 'SYN-S',
  display: 'Synergy - susceptible',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_SignificantChangeUp: Coding = {
  code: 'U',
  display: 'Significant change up',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Unexpected: Coding = {
  code: 'UNE',
  display: 'Unexpected',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_Worse: Coding = {
  code: 'W',
  display: 'Worse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
const v3_ObservationInterpretation_WeaklyReactive: Coding = {
  code: 'WR',
  display: 'Weakly reactive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
};
/**
 * A categorical assessment, providing a rough qualitative interpretation of the observation value,    such as “normal”/ “abnormal”,”low” / “high”, “better” / “worse”, “susceptible” / “resistant”, “expected”/ “not expected”.    The value set is intended to be for ANY use where coded representation of an interpretation is needed.
 *
 *    Notes:
 *
 *    This is being communicated in v2.x in OBX-8 (Observation Interpretation), in v3 in ObservationInterpretation (CWE) in R1 (Representative Realm) and in FHIR in    Observation.interpretation. Historically these values come from the laboratory domain, and these codes are extensively    used. The value set binding is extensible, so codes outside the value set that are needed for interpretation concepts    (i.e. particular meanings) that are not included in the value set can be used, and these new codes may also be added to    the value set and published in a future version.
 */
export const ObservationInterpretation = {
  /**
   * Codes that specify interpretation of genetic analysis, such as "positive", "negative", "carrier", "responsive", etc.
   */
  GeneticObservationInterpretation: v3_ObservationInterpretation_GeneticObservationInterpretation,
  /**
   * Interpretations of change of quantity and/or severity. At most one of B or W and one of U or D allowed.
   */
  ObservationInterpretationChange: v3_ObservationInterpretation_ObservationInterpretationChange,
  /**
   * Technical exceptions resulting in the inability to provide an interpretation. At most one allowed. Does not imply normality or severity.
   */
  ObservationInterpretationExceptions: v3_ObservationInterpretation_ObservationInterpretationExceptions,
  /**
   * Interpretation of normality or degree of abnormality (including critical or "alert" level). Concepts in this category are mutually exclusive, i.e., at most one is allowed.
   */
  ObservationInterpretationNormality: v3_ObservationInterpretation_ObservationInterpretationNormality,
  /**
   * Interpretations of anti-microbial susceptibility testing results (microbiology). At most one allowed.
   */
  ObservationInterpretationSusceptibility: v3_ObservationInterpretation_ObservationInterpretationSusceptibility,
  /**
   * The result is below the minimum detection limit (the test procedure or equipment is the limiting factor).
   *
   *                         Synonyms: Below analytical limit, low off scale.
   */
  OffScaleLow: v3_ObservationInterpretation_OffScaleLow,
  /**
   * The result is above the maximum quantifiable limit (the test procedure or equipment is the limiting factor).
   *
   *                         Synonyms: Above analytical limit, high off scale.
   */
  OffScaleHigh: v3_ObservationInterpretation_OffScaleHigh,
  /**
   * The result or observation value is outside the reference range or expected norm (as defined for the respective test procedure).
   *
   *                         [Note: Typically applies to non-numeric results.]
   */
  Abnormal: v3_ObservationInterpretation_Abnormal,
  /**
   * The result or observation value is outside a reference range or expected norm at a level at which immediate action should be considered for patient safety (as defined for the respective test procedure).
   *
   *                         [Note: Typically applies to non-numeric results.  Analogous to critical/panic limits for numeric results.]
   */
  CriticalAbnormal: v3_ObservationInterpretation_CriticalAbnormal,
  /**
   * The current result or observation value has improved compared to the previous result or observation value (the change is significant as defined in the respective test procedure).
   *
   *                         [Note: This can be applied to quantitative or qualitative observations.]
   */
  Better: v3_ObservationInterpretation_Better,
  /**
   * The patient is considered as carrier based on the testing results. A carrier is an individual who carries an altered form of a gene which can lead to having a child or offspring in future generations with a genetic disorder.
   */
  Carrier: v3_ObservationInterpretation_Carrier,
  /**
   * The current result has decreased from the previous result for a quantitative observation (the change is significant as defined in the respective test procedure).
   */
  SignificantChangeDown: v3_ObservationInterpretation_SignificantChangeDown,
  /**
   * The measurement of the specified component / analyte, organism or clinical sign above the limit of detection of the performed test or procedure.
   */
  Detected: v3_ObservationInterpretation_Detected,
  /**
   * The test or procedure was successfully performed, but the results are borderline and can neither be declared positive / negative nor detected / not detected according to the current established criteria.
   */
  Equivocal: v3_ObservationInterpretation_Equivocal,
  /**
   * The observation/test result is interpreted as being outside the inclusion range for a particular protocol within which the result is being reported.
   *
   *                         Example: A positive result on a Hepatitis screening test.
   *                            Open Issue: EX, HX, LX: These three concepts do not seem to meet a clear need in the vocabulary, and their use in observation interpretation appears likely to be covered by other existing concepts (e.g., A, H, L).  The only apparent significant difference is their reference to use in protocols for exclusion of study subjects.
   * These concepts/codes were proposed by RCRIM for use in the CTLaboratory message.  They were submitted and approved in the November 2005 Harmonization cycle in proposal "030103C_VOCAB_RCRIM_l_quade_RCRIM Obs Interp_20051028154455".  However, this proposal was not fully implemented in the vocabulary.  The proposal recommended creation of the x_ClinicalResearchExclusion domain in ObservationInterpretation with a value set including those three concepts/codes, but there is no subdomain of that name or equivalent with a binding to either of the value sets that contain these concepts/codes.
   * Members of the OO WG have recently attempted to contact members of RCRIM regarding these concepts, both by email and at the recent WGM in Atlanta, without response.  It is felt by OO that the best course of action to take at this time is to add this comprehensive Open Issue rather than deprecate these three concepts at this time, until further discussion is held.
   */
  OutsideThreshold: v3_ObservationInterpretation_OutsideThreshold,
  /**
   * This result has been evaluated in light of known contraindicators.  Once those contraindicators have been taken into account the result is determined to be "Expected"  (e.g., presence of drugs in a patient that is taking prescription medication for pain management).
   */
  Expected: v3_ObservationInterpretation_Expected,
  /**
   * The result for a quantitative observation is above the upper limit of the reference range (as defined for the respective test procedure).
   *
   *                         Synonym: Above high normal
   */
  High: v3_ObservationInterpretation_High,
  /**
   * The result for a quantitative observation is above a reference level at which immediate action should be considered for patient safety (as defined for the respective test procedure).
   *
   *                         Synonym: Above upper panic limits.
   */
  CriticalHigh: v3_ObservationInterpretation_CriticalHigh,
  /**
   * A test result that is significantly higher than the reference (normal) or therapeutic interval, but has not reached the critically high value and might need special attention, as defined by the laboratory or the clinician.
   */
  SignificantlyHigh: v3_ObservationInterpretation_SignificantlyHigh,
  /**
   * The observation/test result is interpreted as being outside the inclusion range for a particular protocol within which the result is being reported.
   *
   *                         Example: A positive result on a Hepatitis screening test.
   *                            Open Issue: EX, HX, LX: These three concepts do not seem to meet a clear need in the vocabulary, and their use in observation interpretation appears likely to be covered by other existing concepts (e.g., A, H, L).  The only apparent significant difference is their reference to use in protocols for exclusion of study subjects.  These concepts/codes were proposed by RCRIM for use in the CTLaboratory message.  They were submitted and approved in the November 2005 Harmonization cycle in proposal "030103C_VOCAB_RCRIM_l_quade_RCRIM Obs Interp_20051028154455". However, this proposal was not fully implemented in the vocabulary.  The proposal recommended creation of the x_ClinicalResearchExclusion domain in ObservationInterpretation with a value set including those three concepts/codes, but there is no subdomain of that name or equivalent with a binding to either of the value sets that contain these concepts/codes.  Members of the OO WG have recently attempted to contact members of RCRIM regarding these concepts, both by email and at the recent WGM in Atlanta, without response.  It is felt by OO that the best course of action to take at this time is to add this comprehensive Open Issue rather than deprecate these three concepts at this time, until further discussion is held.
   */
  AboveHighThreshold: v3_ObservationInterpretation_AboveHighThreshold,
  /**
   * Bacterial strain inhibited in vitro by a concentration of an antimicrobial agent that is associated with uncertain therapeutic effect. Reference: CLSI (http://www.clsi.org/Content/NavigationMenu/Resources/HarmonizedTerminologyDatabase/Harmonized_Terminolo.htm)
   * Projects: ISO 20776-1, ISO 20776-2
   *
   *                         [Note 1: Bacterial strains are categorized as intermediate by applying the appropriate breakpoints in a defined phenotypic test system.]
   *
   *                         [Note 2: This class of susceptibility implies that an infection due to the isolate can be appropriately treated in body sites where the drugs are physiologically concentrated or when a high dosage of drug can be used.]
   *
   *                         [Note 3: This class also indicates a "buffer zone," to prevent small, uncontrolled, technical factors from causing major discrepancies in interpretations.]
   *
   *                         [Note 4: These breakpoints can be altered due to changes in circumstances (e.g., changes in commonly used drug dosages, emergence of new resistance mechanisms).]
   */
  Intermediate: v3_ObservationInterpretation_Intermediate,
  /**
   * There is insufficient evidence that the species in question is a good target for therapy with the drug.  A categorical interpretation is not possible.
   *
   *                         [Note: A MIC with "IE" and/or a comment may be reported (without an accompanying S, I or R-categorization).]
   */
  InsufficientEvidence: v3_ObservationInterpretation_InsufficientEvidence,
  /**
   * The specified component / analyte, organism or clinical sign could neither be declared positive / negative nor detected / not detected by the performed test or procedure.
   *
   *
   *                            Usage Note: For example, if the specimen was degraded, poorly processed, or was missing the required anatomic structures, then "indeterminate" (i.e. "cannot be determined") is the appropriate response, not "equivocal".
   */
  Indeterminate: v3_ObservationInterpretation_Indeterminate,
  /**
   * The result for a quantitative observation is below the lower limit of the reference range (as defined for the respective test procedure).
   *
   *                         Synonym: Below low normal
   */
  Low: v3_ObservationInterpretation_Low,
  /**
   * The result for a quantitative observation is below a reference level at which immediate action should be considered for patient safety (as defined for the respective test procedure).
   *
   *                         Synonym: Below lower panic limits.
   */
  CriticalLow: v3_ObservationInterpretation_CriticalLow,
  /**
   * A test result that is significantly lower than the reference (normal) or therapeutic interval, but has not reached the critically low value and might need special attention, as defined by the laboratory or the clinician.
   */
  SignificantlyLow: v3_ObservationInterpretation_SignificantlyLow,
  /**
   * The numeric observation/test result is interpreted as being below the low threshold value for a particular protocol within which the result is being reported.
   *
   *                         Example: A Total White Blood Cell Count falling below a protocol-defined threshold value of 3000/mm^3
   *                            Open Issue: EX, HX, LX: These three concepts do not seem to meet a clear need in the vocabulary, and their use in observation interpretation appears likely to be covered by other existing concepts (e.g., A, H, L).  The only apparent significant difference is their reference to use in protocols for exclusion of study subjects.  These concepts/codes were proposed by RCRIM for use in the CTLaboratory message.  They were submitted and approved in the November 2005 Harmonization cycle in proposal "030103C_VOCAB_RCRIM_l_quade_RCRIM Obs Interp_20051028154455".  However, this proposal was not fully implemented in the vocabulary.  The proposal recommended creation of the x_ClinicalResearchExclusion domain in ObservationInterpretation with a value set including those three concepts/codes, but there is no subdomain of that name or equivalent with a binding to either of the value sets that contain these concepts/codes.  Members of the OO WG have recently attempted to contact members of RCRIM regarding these concepts, both by email and at the recent WGM in Atlanta, without response.  It is felt by OO that the best course of action to take at this time is to add this comprehensive Open Issue rather than deprecate these three concepts at this time, until further discussion is held.
   */
  BelowLowThreshold: v3_ObservationInterpretation_BelowLowThreshold,
  /**
   * The result or observation value is within the reference range or expected norm (as defined for the respective test procedure).
   *
   *                         [Note: Applies to numeric or non-numeric results.]
   */
  Normal: v3_ObservationInterpretation_Normal,
  /**
   * Use when not enough clinical trial data published by the Clinical and Laboratory Standards Institutes (CLSI) is available to establish the breakpoints for susceptible / intermediate and resistant.
   */
  NoCLSIDefinedBreakpoint: v3_ObservationInterpretation_NoCLSIDefinedBreakpoint,
  /**
   * The presence of the specified component / analyte, organism or clinical sign could not be determined within the limit of detection of the performed test or procedure.
   */
  NotDetected: v3_ObservationInterpretation_NotDetected,
  /**
   * An absence finding of the specified component / analyte, organism or clinical sign based on the established threshold of the performed test or procedure.
   *
   *                         [Note: Negative does not necessarily imply the complete absence of the specified item.]
   */
  Negative: v3_ObservationInterpretation_Negative,
  /**
   * An absence finding used to indicate that the specified component / analyte did not react measurably with the reagent.
   */
  NonReactive: v3_ObservationInterpretation_NonReactive,
  /**
   * A category used for isolates for which only a susceptible interpretive criterion has been designated because of the absence or rare occurrence of resistant strains. Isolates that have MICs above or zone diameters below the value indicated for the susceptible breakpoint should be reported as non-susceptible.
   *
   *                         NOTE 1: An isolate that is interpreted as non-susceptible does not necessarily mean that the isolate has a resistance mechanism. It is possible that isolates with MICs above the susceptible breakpoint that lack resistance mechanisms may be encountered within the wild-type distribution subsequent to the time the susceptible-only breakpoint is set.
   *
   *                         NOTE 2: For strains yielding results in the "nonsusceptible" category, organism identification and antimicrobial susceptibility test results should be confirmed.
   *
   *                         Synonym: decreased susceptibility.
   */
  NonSusceptible: v3_ObservationInterpretation_NonSusceptible,
  /**
   * Interpretations of the presence or absence of a component / analyte or organism in a test or of a sign in a clinical observation. In keeping with laboratory data processing practice, these concepts provide a categorical interpretation of the "meaning" of the quantitative value for the same observation.
   */
  ObservationInterpretationDetection: v3_ObservationInterpretation_ObservationInterpretationDetection,
  /**
   * Interpretation of the observed result taking into account additional information (contraindicators) about the patient's situation. Concepts in this category are mutually exclusive, i.e., at most one is allowed.
   */
  ObservationInterpretationExpectation: v3_ObservationInterpretation_ObservationInterpretationExpectation,
  /**
   * A presence finding of the specified component / analyte, organism or clinical sign based on the established threshold of the performed test or procedure.
   */
  Positive: v3_ObservationInterpretation_Positive,
  /**
   * Bacterial strain inhibited in vitro by a concentration of an antimicrobial agent that is associated with a high likelihood of therapeutic failure.
   * Reference: CLSI (http://www.clsi.org/Content/NavigationMenu/Resources/HarmonizedTerminologyDatabase/Harmonized_Terminolo.htm)
   * Projects: ISO 20776-1, ISO 20776-2
   *
   *                         [Note 1: Bacterial strains are categorized as resistant by applying the appropriate breakpoints in a defined phenotypic test system.]
   *
   *                         [Note 2: This breakpoint can be altered due to changes in circumstances (e.g., changes in commonly used drug dosages, emergence of new resistance mechanisms).]
   */
  Resistant: v3_ObservationInterpretation_Resistant,
  /**
   * Interpretations of the presence and level of reactivity of the specified component / analyte with the reagent in the performed laboratory test.
   */
  ReactivityObservationInterpretation: v3_ObservationInterpretation_ReactivityObservationInterpretation,
  /**
   * A presence finding used to indicate that the specified component / analyte reacted with the reagent above the reliably measurable limit of the performed test.
   */
  Reactive: v3_ObservationInterpretation_Reactive,
  /**
   * Bacterial strain inhibited by in vitro concentration of an antimicrobial agent that is associated with a high likelihood of therapeutic success.
   * Reference: CLSI (http://www.clsi.org/Content/NavigationMenu/Resources/HarmonizedTerminologyDatabase/Harmonized_Terminolo.htm)
   * Synonym (earlier term): Sensitive Projects: ISO 20776-1, ISO 20776-2
   *
   *                         [Note 1: Bacterial strains are categorized as susceptible by applying the appropriate breakpoints in a defined phenotypic system.]
   *
   *                         [Note 2: This breakpoint can be altered due to changes in circumstances (e.g., changes in commonly used drug dosages, emergence of new resistance mechanisms).]
   */
  Susceptible: v3_ObservationInterpretation_Susceptible,
  /**
   * A category that includes isolates with antimicrobial agent minimum inhibitory concentrations (MICs) that approach usually attainable blood and tissue levels and for which response rates may be lower than for susceptible isolates.
   *
   *                         Reference: CLSI document M44-A2 2009 "Method for antifungal disk diffusion susceptibility testing of yeasts; approved guideline - second edition" - page 2.
   */
  SusceptibleDoseDependent: v3_ObservationInterpretation_SusceptibleDoseDependent,
  /**
   * A category for isolates where the bacteria (e.g. enterococci) are not susceptible in vitro to a combination therapy (e.g., high-level aminoglycoside and cell wall active agent).  This is predictive that this combination therapy will not be effective.
   *
   *
   *                            Usage Note: Since the use of penicillin or ampicillin alone often results in treatment failure of serious enterococcal or other bacterial infections, combination therapy is usually indicated to enhance bactericidal activity. The synergy between a cell wall active agent (such as penicillin, ampicillin, or vancomycin) and an aminoglycoside (such as gentamicin, kanamycin or streptomycin) is best predicted by screening for high-level bacterial resistance to the aminoglycoside.
   *
   *
   *                            Open Issue: The print name of the code is very general and the description is very specific to a pair of classes of agents, which may lead to confusion of these concepts in the future should other synergies be found.
   */
  SynergyResistant: v3_ObservationInterpretation_SynergyResistant,
  /**
   * A category for isolates where the bacteria (e.g. enterococci) are susceptible in vitro to a combination therapy (e.g., high-level aminoglycoside and cell wall active agent).  This is predictive that this combination therapy will be effective.
   *
   *
   *                            Usage Note: Since the use of penicillin or ampicillin alone often results in treatment failure of serious enterococcal or other bacterial infections, combination therapy is usually indicated to enhance bactericidal activity. The synergy between a cell wall active agent (such as penicillin, ampicillin, or vancomycin) and an aminoglycoside (such as gentamicin, kanamycin or streptomycin) is best predicted by screening for high-level bacterial resistance to the aminoglycoside.
   *
   *
   *                            Open Issue: The print name of the code is very general and the description is very specific to a pair of classes of agents, which may lead to confusion of these concepts in the future should other synergies be found.
   */
  SynergySusceptible: v3_ObservationInterpretation_SynergySusceptible,
  /**
   * The current result has increased from the previous result for a quantitative observation (the change is significant as defined in the respective test procedure).
   */
  SignificantChangeUp: v3_ObservationInterpretation_SignificantChangeUp,
  /**
   * This result has been evaluated in light of known contraindicators.  Once those contraindicators have been taken into account the result is determined to be "Unexpected" (e.g., presence of non-prescribed drugs in a patient that is taking prescription medication for pain management).
   */
  Unexpected: v3_ObservationInterpretation_Unexpected,
  /**
   * The current result or observation value has degraded compared to the previous result or observation value (the change is significant as defined in the respective test procedure).
   *
   *                         [Note: This can be applied to quantitative or qualitative observations.]
   */
  Worse: v3_ObservationInterpretation_Worse,
  /**
   * A weighted presence finding used to indicate that the specified component / analyte reacted with the reagent, but below the reliably measurable limit of the performed test.
   */
  WeaklyReactive: v3_ObservationInterpretation_WeaklyReactive,
};
const observation_range_category_AbsoluteRange: Coding = {
  code: 'absolute',
  display: 'absolute range',
  system: 'http://hl7.org/fhir/observation-range-category',
};
const observation_range_category_CriticalRange: Coding = {
  code: 'critical',
  display: 'critical range',
  system: 'http://hl7.org/fhir/observation-range-category',
};
const observation_range_category_ReferenceRange: Coding = {
  code: 'reference',
  display: 'reference range',
  system: 'http://hl7.org/fhir/observation-range-category',
};
/**
 * Codes identifying the category of observation range.
 */
export const ObservationRangeCategory = {
  /**
   * Absolute Range for Ordinal and Continuous Observations. Results outside this range are not possible.
   */
  AbsoluteRange: observation_range_category_AbsoluteRange,
  /**
   * Critical Range for Ordinal and Continuous Observations.
   */
  CriticalRange: observation_range_category_CriticalRange,
  /**
   * Reference (Normal) Range for Ordinal and Continuous Observations.
   */
  ReferenceRange: observation_range_category_ReferenceRange,
};
const observation_statistics_VAL20thPercentile: Coding = {
  code: '20-percent',
  display: '20th Percentile',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_QuartileDeviation: Coding = {
  code: '4-dev',
  display: 'Quartile Deviation',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_LowerQuartile: Coding = {
  code: '4-lower',
  display: 'Lower Quartile',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_UpperQuartile: Coding = {
  code: '4-upper',
  display: 'Upper Quartile',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_VAL1stQuintile: Coding = {
  code: '5-1',
  display: '1st Quintile',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_VAL2ndQuintile: Coding = {
  code: '5-2',
  display: '2nd Quintile',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_VAL3rdQuintile: Coding = {
  code: '5-3',
  display: '3rd Quintile',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_VAL4thQuintile: Coding = {
  code: '5-4',
  display: '4th Quintile',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_VAL80thPercentile: Coding = {
  code: '80-percent',
  display: '80th Percentile',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_Average: Coding = {
  code: 'average',
  display: 'Average',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_Count: Coding = {
  code: 'count',
  display: 'Count',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_Kurtosis: Coding = {
  code: 'kurtosis',
  display: 'Kurtosis',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_Maximum: Coding = {
  code: 'maximum',
  display: 'Maximum',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_Median: Coding = {
  code: 'median',
  display: 'Median',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_Minimum: Coding = {
  code: 'minimum',
  display: 'Minimum',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_Regression: Coding = {
  code: 'regression',
  display: 'Regression',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_Skew: Coding = {
  code: 'skew',
  display: 'Skew',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_StandardDeviation: Coding = {
  code: 'std-dev',
  display: 'Standard Deviation',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_Sum: Coding = {
  code: 'sum',
  display: 'Sum',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_TotalCount: Coding = {
  code: 'total-count',
  display: 'Total Count',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
const observation_statistics_Variance: Coding = {
  code: 'variance',
  display: 'Variance',
  system: 'http://terminology.hl7.org/CodeSystem/observation-statistics',
};
/**
 * The statistical operation parameter -"statistic" codes.
 */
export const ObservationStatistics = {
  /**
   * The 20th [Percentile](https://en.wikipedia.org/wiki/Percentile) of N measurements over the stated period.
   */
  VAL20thPercentile: observation_statistics_VAL20thPercentile,
  /**
   * The difference between the upper and lower [Quartiles](https://en.wikipedia.org/wiki/Quartile) is called the Interquartile range. (IQR = Q3-Q1) Quartile deviation or Semi-interquartile range is one-half the difference between the first and the third quartiles.
   */
  QuartileDeviation: observation_statistics_QuartileDeviation,
  /**
   * The lower [Quartile](https://en.wikipedia.org/wiki/Quartile) Boundary of N measurements over the stated period.
   */
  LowerQuartile: observation_statistics_LowerQuartile,
  /**
   * The upper [Quartile](https://en.wikipedia.org/wiki/Quartile) Boundary of N measurements over the stated period.
   */
  UpperQuartile: observation_statistics_UpperQuartile,
  /**
   * The lowest of four values that divide the N measurements into a frequency distribution of five classes with each containing one fifth of the total population.
   */
  VAL1stQuintile: observation_statistics_VAL1stQuintile,
  /**
   * The second of four values that divide the N measurements into a frequency distribution of five classes with each containing one fifth of the total population.
   */
  VAL2ndQuintile: observation_statistics_VAL2ndQuintile,
  /**
   * The third of four values that divide the N measurements into a frequency distribution of five classes with each containing one fifth of the total population.
   */
  VAL3rdQuintile: observation_statistics_VAL3rdQuintile,
  /**
   * The fourth of four values that divide the N measurements into a frequency distribution of five classes with each containing one fifth of the total population.
   */
  VAL4thQuintile: observation_statistics_VAL4thQuintile,
  /**
   * The 80th [Percentile](https://en.wikipedia.org/wiki/Percentile) of N measurements over the stated period.
   */
  VAL80thPercentile: observation_statistics_VAL80thPercentile,
  /**
   * The [mean](https://en.wikipedia.org/wiki/Arithmetic_mean) of N measurements over the stated period.
   */
  Average: observation_statistics_Average,
  /**
   * The [number] of valid measurements over the stated period that contributed to the other statistical outputs.
   */
  Count: observation_statistics_Count,
  /**
   * Kurtosis  is a measure of the "tailedness" of the probability distribution of a real-valued random variable.   Source: [Wikipedia](https://en.wikipedia.org/wiki/Kurtosis).
   */
  Kurtosis: observation_statistics_Kurtosis,
  /**
   * The [maximum](https://en.wikipedia.org/wiki/Maximal_element) value of N measurements over the stated period.
   */
  Maximum: observation_statistics_Maximum,
  /**
   * The [median](https://en.wikipedia.org/wiki/Median) of N measurements over the stated period.
   */
  Median: observation_statistics_Median,
  /**
   * The [minimum](https://en.wikipedia.org/wiki/Minimal_element) value of N measurements over the stated period.
   */
  Minimum: observation_statistics_Minimum,
  /**
   * Linear regression is an approach for modeling two-dimensional sample points with one independent variable and one dependent variable (conventionally, the x and y coordinates in a Cartesian coordinate system) and finds a linear function (a non-vertical straight line) that, as accurately as possible, predicts the dependent variable values as a function of the independent variables. Source: [Wikipedia](https://en.wikipedia.org/wiki/Simple_linear_regression)  This Statistic code will return both a gradient and an intercept value.
   */
  Regression: observation_statistics_Regression,
  /**
   * Skewness is a measure of the asymmetry of the probability distribution of a real-valued random variable about its mean. The skewness value can be positive or negative, or even undefined.  Source: [Wikipedia](https://en.wikipedia.org/wiki/Skewness).
   */
  Skew: observation_statistics_Skew,
  /**
   * The [standard deviation](https://en.wikipedia.org/wiki/Standard_deviation) of N measurements over the stated period.
   */
  StandardDeviation: observation_statistics_StandardDeviation,
  /**
   * The [sum](https://en.wikipedia.org/wiki/Summation) of N measurements over the stated period.
   */
  Sum: observation_statistics_Sum,
  /**
   * The total [number] of valid measurements over the stated period, including observations that were ignored because they did not contain valid result values.
   */
  TotalCount: observation_statistics_TotalCount,
  /**
   * The [variance](https://en.wikipedia.org/wiki/Variance) of N measurements over the stated period.
   */
  Variance: observation_statistics_Variance,
};
const observation_status_Amended: Coding = {
  code: 'amended',
  display: 'Amended',
  system: 'http://hl7.org/fhir/observation-status',
};
const observation_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/observation-status',
};
const observation_status_Corrected: Coding = {
  code: 'corrected',
  display: 'Corrected',
  system: 'http://hl7.org/fhir/observation-status',
};
const observation_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/observation-status',
};
const observation_status_Final: Coding = {
  code: 'final',
  display: 'Final',
  system: 'http://hl7.org/fhir/observation-status',
};
const observation_status_Preliminary: Coding = {
  code: 'preliminary',
  display: 'Preliminary',
  system: 'http://hl7.org/fhir/observation-status',
};
const observation_status_Registered: Coding = {
  code: 'registered',
  display: 'Registered',
  system: 'http://hl7.org/fhir/observation-status',
};
const observation_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/observation-status',
};
/**
 * Codes providing the status of an observation.
 */
export const ObservationStatus = {
  /**
   * Subsequent to being Final, the observation has been modified subsequent.  This includes updates/new information and corrections.
   */
  Amended: observation_status_Amended,
  /**
   * The observation is unavailable because the measurement was not started or not completed (also sometimes called "aborted").
   */
  Cancelled: observation_status_Cancelled,
  /**
   * Subsequent to being Final, the observation has been modified to correct an error in the test result.
   */
  Corrected: observation_status_Corrected,
  /**
   * The observation has been withdrawn following previous final release.  This electronic record should never have existed, though it is possible that real-world decisions were based on it. (If real-world activity has occurred, the status should be "cancelled" rather than "entered-in-error".).
   */
  EnteredInError: observation_status_EnteredInError,
  /**
   * The observation is complete and there are no further actions needed. Additional information such "released", "signed", etc would be represented using [Provenance](provenance.html) which provides not only the act but also the actors and dates and other related data. These act states would be associated with an observation status of `preliminary` until they are all completed and then a status of `final` would be applied.
   */
  Final: observation_status_Final,
  /**
   * This is an initial or interim observation: data may be incomplete or unverified.
   */
  Preliminary: observation_status_Preliminary,
  /**
   * The existence of the observation is registered, but there is no result yet available.
   */
  Registered: observation_status_Registered,
  /**
   * The authoring/source system does not know which of the status values currently applies for this observation. Note: This concept is not to be used for "other" - one of the listed statuses is presumed to apply, but the authoring/source system does not know which.
   */
  Unknown: observation_status_Unknown,
};
const ObservationVitalsignresult_VAL27086: Coding = {
  code: '2708-6',
  system: 'http://loinc.org',
};
const ObservationVitalsignresult_VAL294637: Coding = {
  code: '29463-7',
  system: 'http://loinc.org',
};
const ObservationVitalsignresult_VAL391565: Coding = {
  code: '39156-5',
  system: 'http://loinc.org',
};
const ObservationVitalsignresult_VAL83022: Coding = {
  code: '8302-2',
  system: 'http://loinc.org',
};
const ObservationVitalsignresult_VAL83105: Coding = {
  code: '8310-5',
  system: 'http://loinc.org',
};
const ObservationVitalsignresult_VAL84624: Coding = {
  code: '8462-4',
  system: 'http://loinc.org',
};
const ObservationVitalsignresult_VAL84780: Coding = {
  code: '8478-0',
  system: 'http://loinc.org',
};
const ObservationVitalsignresult_VAL84806: Coding = {
  code: '8480-6',
  system: 'http://loinc.org',
};
const ObservationVitalsignresult_VAL853531: Coding = {
  code: '85353-1',
  system: 'http://loinc.org',
};
const ObservationVitalsignresult_VAL853549: Coding = {
  code: '85354-9',
  system: 'http://loinc.org',
};
const ObservationVitalsignresult_VAL88674: Coding = {
  code: '8867-4',
  system: 'http://loinc.org',
};
const ObservationVitalsignresult_VAL92791: Coding = {
  code: '9279-1',
  system: 'http://loinc.org',
};
const ObservationVitalsignresult_VAL98434: Coding = {
  code: '9843-4',
  system: 'http://loinc.org',
};
/**
 * This value set indicates the allowed vital sign result types.   The LOINC code for Vitals Signs panel (85353-1) is a grouping structure for a set of vital signs and includes related links (with type=has-member) to the Observations in this set (e.g. respiratory rate, heart rate, BP).  The Blood pressure panel (85354-9) is used to group the component observations Systolic blood pressure (8480-6) and Diastolic blood pressure (8462-4).
 */
export const ObservationVitalsignresult = {
  VAL27086: ObservationVitalsignresult_VAL27086,
  VAL294637: ObservationVitalsignresult_VAL294637,
  VAL391565: ObservationVitalsignresult_VAL391565,
  VAL83022: ObservationVitalsignresult_VAL83022,
  VAL83105: ObservationVitalsignresult_VAL83105,
  VAL84624: ObservationVitalsignresult_VAL84624,
  VAL84780: ObservationVitalsignresult_VAL84780,
  VAL84806: ObservationVitalsignresult_VAL84806,
  VAL853531: ObservationVitalsignresult_VAL853531,
  VAL853549: ObservationVitalsignresult_VAL853549,
  VAL88674: ObservationVitalsignresult_VAL88674,
  VAL92791: ObservationVitalsignresult_VAL92791,
  VAL98434: ObservationVitalsignresult_VAL98434,
};
const operation_kind_Operation: Coding = {
  code: 'operation',
  display: 'Operation',
  system: 'http://hl7.org/fhir/operation-kind',
};
const operation_kind_Query: Coding = {
  code: 'query',
  display: 'Query',
  system: 'http://hl7.org/fhir/operation-kind',
};
/**
 * Whether an operation is a normal operation or a query.
 */
export const OperationKind = {
  /**
   * This operation is invoked as an operation.
   */
  Operation: operation_kind_Operation,
  /**
   * This operation is a named query, invoked using the search mechanism.
   */
  Query: operation_kind_Query,
};
const operation_outcome_DELETEMULTIPLEMATCHES: Coding = {
  code: 'DELETE_MULTIPLE_MATCHES',
  display: 'Error: Multiple matches exist for the conditional delete',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGAUTHREQUIRED: Coding = {
  code: 'MSG_AUTH_REQUIRED',
  display: 'You must authenticate before you can use this service',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGBADFORMAT: Coding = {
  code: 'MSG_BAD_FORMAT',
  display: 'Bad Syntax: "%s" must be a %s\'',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGBADSYNTAX: Coding = {
  code: 'MSG_BAD_SYNTAX',
  display: 'Bad Syntax in %s',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGCANTPARSECONTENT: Coding = {
  code: 'MSG_CANT_PARSE_CONTENT',
  display: 'Unable to parse feed (entry content type = "%s")',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGCANTPARSEROOT: Coding = {
  code: 'MSG_CANT_PARSE_ROOT',
  display: 'Unable to parse feed (root element name = "%s")',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGCREATED: Coding = {
  code: 'MSG_CREATED',
  display: 'New resource created',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGDATEFORMAT: Coding = {
  code: 'MSG_DATE_FORMAT',
  display: 'The Date value %s is not in the correct format (Xml Date Format required)',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGDELETED: Coding = {
  code: 'MSG_DELETED',
  display: 'This resource has been deleted',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGDELETEDDONE: Coding = {
  code: 'MSG_DELETED_DONE',
  display: 'Resource deleted',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGDELETEDID: Coding = {
  code: 'MSG_DELETED_ID',
  display: 'The resource "%s" has been deleted',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGDUPLICATEID: Coding = {
  code: 'MSG_DUPLICATE_ID',
  display: 'Duplicate Id %s for resource type %s',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGERRORPARSING: Coding = {
  code: 'MSG_ERROR_PARSING',
  display: 'Error parsing resource Xml (%s)',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGIDINVALID: Coding = {
  code: 'MSG_ID_INVALID',
  display: 'Id "%s" has an invalid character "%s"',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGIDTOOLONG: Coding = {
  code: 'MSG_ID_TOO_LONG',
  display: 'Id "%s" too long (length limit 36)',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGINVALIDID: Coding = {
  code: 'MSG_INVALID_ID',
  display: 'Id not accepted',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGJSONOBJECT: Coding = {
  code: 'MSG_JSON_OBJECT',
  display: 'Json Source for a resource should start with an object',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGLOCALFAIL: Coding = {
  code: 'MSG_LOCAL_FAIL',
  display: 'Unable to resolve local reference to resource %s',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGNOEXIST: Coding = {
  code: 'MSG_NO_EXIST',
  display: 'Resource Id "%s" does not exist',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGNOMATCH: Coding = {
  code: 'MSG_NO_MATCH',
  display: 'No Resource found matching the query "%s"',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGNOMODULE: Coding = {
  code: 'MSG_NO_MODULE',
  display: 'No module could be found to handle the request "%s"',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGNOSUMMARY: Coding = {
  code: 'MSG_NO_SUMMARY',
  display: 'No Summary for this resource',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGOPNOTALLOWED: Coding = {
  code: 'MSG_OP_NOT_ALLOWED',
  display: 'Operation %s not allowed for resource %s (due to local configuration)',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGPARAMCHAINED: Coding = {
  code: 'MSG_PARAM_CHAINED',
  display: 'Unknown chained parameter name "%s"',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGPARAMINVALID: Coding = {
  code: 'MSG_PARAM_INVALID',
  display: 'Parameter "%s" content is invalid',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGPARAMMODIFIERINVALID: Coding = {
  code: 'MSG_PARAM_MODIFIER_INVALID',
  display: 'Parameter "%s" modifier is invalid',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGPARAMNOREPEAT: Coding = {
  code: 'MSG_PARAM_NO_REPEAT',
  display: 'Parameter "%s" is not allowed to repeat',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGPARAMUNKNOWN: Coding = {
  code: 'MSG_PARAM_UNKNOWN',
  display: 'Parameter "%s" not understood',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGRESOURCEEXAMPLEPROTECTED: Coding = {
  code: 'MSG_RESOURCE_EXAMPLE_PROTECTED',
  display: 'Resources with identity "example" cannot be deleted (for testing/training purposes)',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGRESOURCEIDFAIL: Coding = {
  code: 'MSG_RESOURCE_ID_FAIL',
  display: 'unable to allocate resource id',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGRESOURCEIDMISMATCH: Coding = {
  code: 'MSG_RESOURCE_ID_MISMATCH',
  display: 'Resource Id Mismatch',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGRESOURCEIDMISSING: Coding = {
  code: 'MSG_RESOURCE_ID_MISSING',
  display: 'Resource Id Missing',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGRESOURCENOTALLOWED: Coding = {
  code: 'MSG_RESOURCE_NOT_ALLOWED',
  display: 'Not allowed to submit a resource for this operation',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGRESOURCEREQUIRED: Coding = {
  code: 'MSG_RESOURCE_REQUIRED',
  display: 'A resource is required',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGRESOURCETYPEMISMATCH: Coding = {
  code: 'MSG_RESOURCE_TYPE_MISMATCH',
  display: 'Resource Type Mismatch',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGSORTUNKNOWN: Coding = {
  code: 'MSG_SORT_UNKNOWN',
  display: 'Unknown sort parameter name "%s"',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGTRANSACTIONDUPLICATEID: Coding = {
  code: 'MSG_TRANSACTION_DUPLICATE_ID',
  display: 'Duplicate Identifier in transaction: %s',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGTRANSACTIONMISSINGID: Coding = {
  code: 'MSG_TRANSACTION_MISSING_ID',
  display: 'Missing Identifier in transaction - an entry.id must be provided',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGUNHANDLEDNODETYPE: Coding = {
  code: 'MSG_UNHANDLED_NODE_TYPE',
  display: 'Unhandled xml node type "%s"',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGUNKNOWNCONTENT: Coding = {
  code: 'MSG_UNKNOWN_CONTENT',
  display: 'Unknown Content (%s) at %s',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGUNKNOWNOPERATION: Coding = {
  code: 'MSG_UNKNOWN_OPERATION',
  display: 'unknown FHIR http operation',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGUNKNOWNTYPE: Coding = {
  code: 'MSG_UNKNOWN_TYPE',
  display: 'Resource Type "%s" not recognised',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGUPDATED: Coding = {
  code: 'MSG_UPDATED',
  display: 'existing resource updated',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGVERSIONAWARE: Coding = {
  code: 'MSG_VERSION_AWARE',
  display: 'Version aware updates are required for this resource',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGVERSIONAWARECONFLICT: Coding = {
  code: 'MSG_VERSION_AWARE_CONFLICT',
  display: 'Update Conflict (server current version = "%s", client version referenced = "%s")',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGVERSIONAWAREURL: Coding = {
  code: 'MSG_VERSION_AWARE_URL',
  display: 'Version specific URL not recognised',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_MSGWRONGNS: Coding = {
  code: 'MSG_WRONG_NS',
  display: 'This does not appear to be a FHIR element or resource (wrong namespace "%s")',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_SEARCHMULTIPLE: Coding = {
  code: 'SEARCH_MULTIPLE',
  display: 'Error: Multiple matches exist for %s search parameters "%s"',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_SEARCHNONE: Coding = {
  code: 'SEARCH_NONE',
  display: 'Error: no processable search found for %s search parameters "%s"',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
const operation_outcome_UPDATEMULTIPLEMATCHES: Coding = {
  code: 'UPDATE_MULTIPLE_MATCHES',
  display: 'Error: Multiple matches exist for the conditional update',
  system: 'http://terminology.hl7.org/CodeSystem/operation-outcome',
};
/**
 * Operation Outcome codes used by FHIR test servers (see Implementation file translations.xml)
 */
export const OperationOutcome = {
  DELETEMULTIPLEMATCHES: operation_outcome_DELETEMULTIPLEMATCHES,
  MSGAUTHREQUIRED: operation_outcome_MSGAUTHREQUIRED,
  MSGBADFORMAT: operation_outcome_MSGBADFORMAT,
  MSGBADSYNTAX: operation_outcome_MSGBADSYNTAX,
  MSGCANTPARSECONTENT: operation_outcome_MSGCANTPARSECONTENT,
  MSGCANTPARSEROOT: operation_outcome_MSGCANTPARSEROOT,
  MSGCREATED: operation_outcome_MSGCREATED,
  MSGDATEFORMAT: operation_outcome_MSGDATEFORMAT,
  MSGDELETED: operation_outcome_MSGDELETED,
  MSGDELETEDDONE: operation_outcome_MSGDELETEDDONE,
  MSGDELETEDID: operation_outcome_MSGDELETEDID,
  MSGDUPLICATEID: operation_outcome_MSGDUPLICATEID,
  MSGERRORPARSING: operation_outcome_MSGERRORPARSING,
  MSGIDINVALID: operation_outcome_MSGIDINVALID,
  MSGIDTOOLONG: operation_outcome_MSGIDTOOLONG,
  MSGINVALIDID: operation_outcome_MSGINVALIDID,
  MSGJSONOBJECT: operation_outcome_MSGJSONOBJECT,
  MSGLOCALFAIL: operation_outcome_MSGLOCALFAIL,
  MSGNOEXIST: operation_outcome_MSGNOEXIST,
  MSGNOMATCH: operation_outcome_MSGNOMATCH,
  MSGNOMODULE: operation_outcome_MSGNOMODULE,
  MSGNOSUMMARY: operation_outcome_MSGNOSUMMARY,
  MSGOPNOTALLOWED: operation_outcome_MSGOPNOTALLOWED,
  MSGPARAMCHAINED: operation_outcome_MSGPARAMCHAINED,
  MSGPARAMINVALID: operation_outcome_MSGPARAMINVALID,
  MSGPARAMMODIFIERINVALID: operation_outcome_MSGPARAMMODIFIERINVALID,
  MSGPARAMNOREPEAT: operation_outcome_MSGPARAMNOREPEAT,
  MSGPARAMUNKNOWN: operation_outcome_MSGPARAMUNKNOWN,
  MSGRESOURCEEXAMPLEPROTECTED: operation_outcome_MSGRESOURCEEXAMPLEPROTECTED,
  MSGRESOURCEIDFAIL: operation_outcome_MSGRESOURCEIDFAIL,
  MSGRESOURCEIDMISMATCH: operation_outcome_MSGRESOURCEIDMISMATCH,
  MSGRESOURCEIDMISSING: operation_outcome_MSGRESOURCEIDMISSING,
  MSGRESOURCENOTALLOWED: operation_outcome_MSGRESOURCENOTALLOWED,
  MSGRESOURCEREQUIRED: operation_outcome_MSGRESOURCEREQUIRED,
  MSGRESOURCETYPEMISMATCH: operation_outcome_MSGRESOURCETYPEMISMATCH,
  MSGSORTUNKNOWN: operation_outcome_MSGSORTUNKNOWN,
  MSGTRANSACTIONDUPLICATEID: operation_outcome_MSGTRANSACTIONDUPLICATEID,
  MSGTRANSACTIONMISSINGID: operation_outcome_MSGTRANSACTIONMISSINGID,
  MSGUNHANDLEDNODETYPE: operation_outcome_MSGUNHANDLEDNODETYPE,
  MSGUNKNOWNCONTENT: operation_outcome_MSGUNKNOWNCONTENT,
  MSGUNKNOWNOPERATION: operation_outcome_MSGUNKNOWNOPERATION,
  MSGUNKNOWNTYPE: operation_outcome_MSGUNKNOWNTYPE,
  MSGUPDATED: operation_outcome_MSGUPDATED,
  MSGVERSIONAWARE: operation_outcome_MSGVERSIONAWARE,
  MSGVERSIONAWARECONFLICT: operation_outcome_MSGVERSIONAWARECONFLICT,
  MSGVERSIONAWAREURL: operation_outcome_MSGVERSIONAWAREURL,
  MSGWRONGNS: operation_outcome_MSGWRONGNS,
  SEARCHMULTIPLE: operation_outcome_SEARCHMULTIPLE,
  SEARCHNONE: operation_outcome_SEARCHNONE,
  UPDATEMULTIPLEMATCHES: operation_outcome_UPDATEMULTIPLEMATCHES,
};
const operation_parameter_use_In: Coding = {
  code: 'in',
  display: 'In',
  system: 'http://hl7.org/fhir/operation-parameter-use',
};
const operation_parameter_use_Out: Coding = {
  code: 'out',
  display: 'Out',
  system: 'http://hl7.org/fhir/operation-parameter-use',
};
/**
 * Whether an operation parameter is an input or an output parameter.
 */
export const OperationParameterUse = {
  /**
   * This is an input parameter.
   */
  In: operation_parameter_use_In,
  /**
   * This is an output parameter.
   */
  Out: operation_parameter_use_Out,
};
const oral_prosthodontic_material_FixedBridge: Coding = {
  code: '1',
  display: 'Fixed Bridge',
  system: 'http://hl7.org/fhir/ex-oralprostho',
};
const oral_prosthodontic_material_MarylandBridge: Coding = {
  code: '2',
  display: 'Maryland Bridge',
  system: 'http://hl7.org/fhir/ex-oralprostho',
};
const oral_prosthodontic_material_DentureAcrylic: Coding = {
  code: '3',
  display: 'Denture Acrylic',
  system: 'http://hl7.org/fhir/ex-oralprostho',
};
const oral_prosthodontic_material_DentureChromeCobalt: Coding = {
  code: '4',
  display: 'Denture Chrome Cobalt',
  system: 'http://hl7.org/fhir/ex-oralprostho',
};
/**
 * This value set includes sample Oral Prosthodontic Material type codes.
 */
export const OralProsthodonticMaterial = {
  /**
   * Fixed Bridge
   */
  FixedBridge: oral_prosthodontic_material_FixedBridge,
  /**
   * Maryland Bridge
   */
  MarylandBridge: oral_prosthodontic_material_MarylandBridge,
  /**
   * Denture Acrylic
   */
  DentureAcrylic: oral_prosthodontic_material_DentureAcrylic,
  /**
   * Denture Chrome Cobalt
   */
  DentureChromeCobalt: oral_prosthodontic_material_DentureChromeCobalt,
};
const organization_role_Agency: Coding = {
  code: 'agency',
  display: 'Agency',
  system: 'http://hl7.org/fhir/organization-role',
};
const organization_role_Diagnostics: Coding = {
  code: 'diagnostics',
  display: 'Diagnostics',
  system: 'http://hl7.org/fhir/organization-role',
};
const organization_role_HIEHIO: Coding = {
  code: 'HIE/HIO',
  display: 'HIE/HIO',
  system: 'http://hl7.org/fhir/organization-role',
};
const organization_role_Member: Coding = {
  code: 'member',
  display: 'Member',
  system: 'http://hl7.org/fhir/organization-role',
};
const organization_role_Payer: Coding = {
  code: 'payer',
  display: 'Payer',
  system: 'http://hl7.org/fhir/organization-role',
};
const organization_role_Provider: Coding = {
  code: 'provider',
  display: 'Provider',
  system: 'http://hl7.org/fhir/organization-role',
};
const organization_role_Research: Coding = {
  code: 'research',
  display: 'Research',
  system: 'http://hl7.org/fhir/organization-role',
};
const organization_role_Supplier: Coding = {
  code: 'supplier',
  display: 'Supplier',
  system: 'http://hl7.org/fhir/organization-role',
};
/**
 * This example value set defines a set of codes that can be used to indicate the role of one Organization in relation to another.
 */
export const OrganizationRole = {
  /**
   * An organization such as a public health agency, community/social services provider, etc.
   */
  Agency: organization_role_Agency,
  /**
   * An organization providing diagnostic testing/laboratory services
   */
  Diagnostics: organization_role_Diagnostics,
  /**
   * An organization that facilitates electronic clinical data exchange between entities
   */
  HIEHIO: organization_role_HIEHIO,
  /**
   * A type of non-ownership relationship between entities (encompasses partnerships, collaboration, joint ventures, etc.)
   */
  Member: organization_role_Member,
  /**
   * An organization providing reimbursement, payment, or related services
   */
  Payer: organization_role_Payer,
  Provider: organization_role_Provider,
  /**
   * An organization providing research-related services such as conducting research, recruiting research participants, analyzing data, etc.
   */
  Research: organization_role_Research,
  /**
   * An organization that provides medical supplies (e.g. medical devices, equipment, pharmaceutical products, etc.)
   */
  Supplier: organization_role_Supplier,
};
const organization_type_NonHealthcareBusinessOrCorporation: Coding = {
  code: 'bus',
  display: 'Non-Healthcare Business or Corporation',
  system: 'http://terminology.hl7.org/CodeSystem/organization-type',
};
const organization_type_CommunityGroup: Coding = {
  code: 'cg',
  display: 'Community Group',
  system: 'http://terminology.hl7.org/CodeSystem/organization-type',
};
const organization_type_ClinicalResearchSponsor: Coding = {
  code: 'crs',
  display: 'Clinical Research Sponsor',
  system: 'http://terminology.hl7.org/CodeSystem/organization-type',
};
const organization_type_HospitalDepartment: Coding = {
  code: 'dept',
  display: 'Hospital Department',
  system: 'http://terminology.hl7.org/CodeSystem/organization-type',
};
const organization_type_EducationalInstitute: Coding = {
  code: 'edu',
  display: 'Educational Institute',
  system: 'http://terminology.hl7.org/CodeSystem/organization-type',
};
const organization_type_Government: Coding = {
  code: 'govt',
  display: 'Government',
  system: 'http://terminology.hl7.org/CodeSystem/organization-type',
};
const organization_type_InsuranceCompany: Coding = {
  code: 'ins',
  display: 'Insurance Company',
  system: 'http://terminology.hl7.org/CodeSystem/organization-type',
};
const organization_type_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/organization-type',
};
const organization_type_Payer: Coding = {
  code: 'pay',
  display: 'Payer',
  system: 'http://terminology.hl7.org/CodeSystem/organization-type',
};
const organization_type_HealthcareProvider: Coding = {
  code: 'prov',
  display: 'Healthcare Provider',
  system: 'http://terminology.hl7.org/CodeSystem/organization-type',
};
const organization_type_ReligiousInstitution: Coding = {
  code: 'reli',
  display: 'Religious Institution',
  system: 'http://terminology.hl7.org/CodeSystem/organization-type',
};
const organization_type_OrganizationalTeam: Coding = {
  code: 'team',
  display: 'Organizational team',
  system: 'http://terminology.hl7.org/CodeSystem/organization-type',
};
/**
 * This example value set defines a set of codes that can be used to indicate a type of organization.
 */
export const OrganizationType = {
  /**
   * An organization that is a registered business or corporation but not identified by other types.
   */
  NonHealthcareBusinessOrCorporation: organization_type_NonHealthcareBusinessOrCorporation,
  /**
   * An un-incorporated community group.
   */
  CommunityGroup: organization_type_CommunityGroup,
  /**
   * An organization that is identified as a Pharmaceutical/Clinical Research Sponsor.
   */
  ClinicalResearchSponsor: organization_type_ClinicalResearchSponsor,
  /**
   * A department or ward within a hospital (Generally is not applicable to top level organizations)
   */
  HospitalDepartment: organization_type_HospitalDepartment,
  /**
   * An educational institution that provides education or research facilities.
   */
  EducationalInstitute: organization_type_EducationalInstitute,
  /**
   * A political body, often used when including organization records for government bodies such as a Federal Government, State or Local Government.
   */
  Government: organization_type_Government,
  /**
   * A company that provides insurance to its subscribers that may include healthcare related policies.
   */
  InsuranceCompany: organization_type_InsuranceCompany,
  /**
   * Other type of organization not already specified.
   */
  Other: organization_type_Other,
  /**
   * A company, charity, or governmental organization, which processes claims and/or issues payments to providers on behalf of patients or groups of patients.
   */
  Payer: organization_type_Payer,
  /**
   * An organization that provides healthcare services.
   */
  HealthcareProvider: organization_type_HealthcareProvider,
  /**
   * An organization that is identified as a part of a religious institution.
   */
  ReligiousInstitution: organization_type_ReligiousInstitution,
  /**
   * An organizational team is usually a grouping of practitioners that perform a specific function within an organization (which could be a top level organization, or a department).
   */
  OrganizationalTeam: organization_type_OrganizationalTeam,
};
const orientation_type_AntisenseOrientationOfReferenceSeq: Coding = {
  code: 'antisense',
  display: 'Antisense orientation of referenceSeq',
  system: 'http://hl7.org/fhir/orientation-type',
};
const orientation_type_SenseOrientationOfReferenceSeq: Coding = {
  code: 'sense',
  display: 'Sense orientation of referenceSeq',
  system: 'http://hl7.org/fhir/orientation-type',
};
/**
 * Type for orientation.
 */
export const OrientationType = {
  /**
   * Antisense orientation of reference sequence.
   */
  AntisenseOrientationOfReferenceSeq: orientation_type_AntisenseOrientationOfReferenceSeq,
  /**
   * Sense orientation of reference sequence.
   */
  SenseOrientationOfReferenceSeq: orientation_type_SenseOrientationOfReferenceSeq,
};
const parameter_group_BloodChemistryParameterGroup: Coding = {
  code: 'blood-chemistry',
  display: 'Blood Chemistry Parameter Group',
  system: 'http://terminology.hl7.org/CodeSystem/parameter-group',
};
const parameter_group_DrugDeliveryParameterGroup: Coding = {
  code: 'drug-delivery',
  display: 'Drug Delivery Parameter Group',
  system: 'http://terminology.hl7.org/CodeSystem/parameter-group',
};
const parameter_group_ECGParameterGroup: Coding = {
  code: 'ecg',
  display: 'ECG Parameter Group',
  system: 'http://terminology.hl7.org/CodeSystem/parameter-group',
};
const parameter_group_FluidChemistryParameterGroup: Coding = {
  code: 'fluid-chemistry',
  display: 'Fluid Chemistry Parameter Group',
  system: 'http://terminology.hl7.org/CodeSystem/parameter-group',
};
const parameter_group_HaemodynamicParameterGroup: Coding = {
  code: 'haemodynamic',
  display: 'Haemodynamic Parameter Group',
  system: 'http://terminology.hl7.org/CodeSystem/parameter-group',
};
const parameter_group_MiscellaneousParameterGroup: Coding = {
  code: 'miscellaneous',
  display: 'Miscellaneous Parameter Group',
  system: 'http://terminology.hl7.org/CodeSystem/parameter-group',
};
const parameter_group_NeurologicalParameterGroup: Coding = {
  code: 'neurological',
  display: 'Neurological Parameter Group',
  system: 'http://terminology.hl7.org/CodeSystem/parameter-group',
};
const parameter_group_RespiratoryParameterGroup: Coding = {
  code: 'respiratory',
  display: 'Respiratory Parameter Group',
  system: 'http://terminology.hl7.org/CodeSystem/parameter-group',
};
const parameter_group_VentilationParameterGroup: Coding = {
  code: 'ventilation',
  display: 'Ventilation Parameter Group',
  system: 'http://terminology.hl7.org/CodeSystem/parameter-group',
};
/**
 * Codes identifying groupings of parameters; e.g. Cardiovascular.
 */
export const ParameterGroup = {
  /**
   * Blood Chemistry Parameter Group - MDC_PGRP_BLOOD_CHEM.
   */
  BloodChemistryParameterGroup: parameter_group_BloodChemistryParameterGroup,
  /**
   * Drug Delivery Parameter Group - MDC_PGRP_DRUG.
   */
  DrugDeliveryParameterGroup: parameter_group_DrugDeliveryParameterGroup,
  /**
   * ECG Parameter Group - MDC_PGRP_ECG.
   */
  ECGParameterGroup: parameter_group_ECGParameterGroup,
  /**
   * Fluid Chemistry Parameter Group - MDC_PGRP_FLUID.
   */
  FluidChemistryParameterGroup: parameter_group_FluidChemistryParameterGroup,
  /**
   * Haemodynamic Parameter Group - MDC_PGRP_HEMO.
   */
  HaemodynamicParameterGroup: parameter_group_HaemodynamicParameterGroup,
  /**
   * Miscellaneous Parameter Group - MDC_PGRP_MISC.
   */
  MiscellaneousParameterGroup: parameter_group_MiscellaneousParameterGroup,
  /**
   * Neurological Parameter Group - MDC_PGRP_NEURO.
   */
  NeurologicalParameterGroup: parameter_group_NeurologicalParameterGroup,
  /**
   * Respiratory Parameter Group - MDC_PGRP_RESP.
   */
  RespiratoryParameterGroup: parameter_group_RespiratoryParameterGroup,
  /**
   * Ventilation Parameter Group - MDC_PGRP_VENT.
   */
  VentilationParameterGroup: parameter_group_VentilationParameterGroup,
};
const v3_RoleCode_AdoptiveFather: Coding = {
  code: 'ADOPTF',
  display: 'adoptive father',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AdoptiveMother: Coding = {
  code: 'ADOPTM',
  display: 'adoptive mother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AdoptiveParent: Coding = {
  code: 'ADOPTP',
  display: 'adoptive parent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Father: Coding = {
  code: 'FTH',
  display: 'father',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FosterFather: Coding = {
  code: 'FTHFOST',
  display: 'foster father',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FraternalTwin: Coding = {
  code: 'FTWIN',
  display: 'fraternal twin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_GestationalMother: Coding = {
  code: 'GESTM',
  display: 'gestational mother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_IdenticalTwin: Coding = {
  code: 'ITWIN',
  display: 'identical twin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Mother: Coding = {
  code: 'MTH',
  display: 'mother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FosterMother: Coding = {
  code: 'MTHFOST',
  display: 'foster mother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NaturalFather: Coding = {
  code: 'NFTH',
  display: 'natural father',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NaturalFatherOfFetus: Coding = {
  code: 'NFTHF',
  display: 'natural father of fetus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NaturalMother: Coding = {
  code: 'NMTH',
  display: 'natural mother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NaturalMotherOfFetus: Coding = {
  code: 'NMTHF',
  display: 'natural mother of fetus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NaturalParent: Coding = {
  code: 'NPRN',
  display: 'natural parent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Parent: Coding = {
  code: 'PRN',
  display: 'parent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FosterParent: Coding = {
  code: 'PRNFOST',
  display: 'foster parent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Stepfather: Coding = {
  code: 'STPFTH',
  display: 'stepfather',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Stepmother: Coding = {
  code: 'STPMTH',
  display: 'stepmother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_StepParent: Coding = {
  code: 'STPPRN',
  display: 'step parent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Twin: Coding = {
  code: 'TWIN',
  display: 'twin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
/**
 * The value set includes the v3 RoleCode PRN (parent), TWIN (twin) and all of their specializations.  It covers the relationships needed to establish genetic pedigree relationships between family members.
 */
export const ParentRelationshipCodes = {
  /**
   * The player of the role (father) is a male who has taken the scoper (child) into their family through legal means and raises them as his own child.
   */
  AdoptiveFather: v3_RoleCode_AdoptiveFather,
  /**
   * The player of the role (father) is a female who has taken the scoper (child) into their family through legal means and raises them as her own child.
   */
  AdoptiveMother: v3_RoleCode_AdoptiveMother,
  /**
   * The player of the role (parent) has taken the scoper (child) into their family through legal means and raises them as his or her own child.
   */
  AdoptiveParent: v3_RoleCode_AdoptiveParent,
  /**
   * The player of the role is a male who begets or raises or nurtures the scoping entity (child).
   */
  Father: v3_RoleCode_Father,
  /**
   * The player of the role (parent) who is a male state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterFather: v3_RoleCode_FosterFather,
  /**
   * The scoper and player were carried in the same womb and share common biological parents but are the product of distinct egg/sperm pairs.
   */
  FraternalTwin: v3_RoleCode_FraternalTwin,
  /**
   * The player is a female whose womb carries the fetus of the scoper.  Generally used when the gestational mother and natural mother are not the same.
   */
  GestationalMother: v3_RoleCode_GestationalMother,
  /**
   * The scoper and player are offspring of the same egg-sperm pair.
   */
  IdenticalTwin: v3_RoleCode_IdenticalTwin,
  /**
   * The player of the role is a female who conceives, gives birth to, or raises and nurtures the scoping entity (child).
   */
  Mother: v3_RoleCode_Mother,
  /**
   * The player of the role (parent) who is a female state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterMother: v3_RoleCode_FosterMother,
  /**
   * The player of the role is a male who begets the scoping entity (child).
   */
  NaturalFather: v3_RoleCode_NaturalFather,
  /**
   * Indicates the biologic male parent of a fetus.
   */
  NaturalFatherOfFetus: v3_RoleCode_NaturalFatherOfFetus,
  /**
   * The player of the role is a female who conceives or gives birth to the scoping entity (child).
   */
  NaturalMother: v3_RoleCode_NaturalMother,
  /**
   * The player is the biologic female parent of the scoping fetus.
   */
  NaturalMotherOfFetus: v3_RoleCode_NaturalMotherOfFetus,
  /**
   * natural parent
   */
  NaturalParent: v3_RoleCode_NaturalParent,
  /**
   * The player of the role is one who begets, gives birth to, or nurtures and raises the scoping entity (child).
   */
  Parent: v3_RoleCode_Parent,
  /**
   * The player of the role (parent) who is a state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterParent: v3_RoleCode_FosterParent,
  /**
   * The player of the role is the husband of scoping person's mother and not the scoping person's natural father.
   */
  Stepfather: v3_RoleCode_Stepfather,
  /**
   * The player of the role is the wife of scoping person's father and not the scoping person's natural mother.
   */
  Stepmother: v3_RoleCode_Stepmother,
  /**
   * The player of the role is the spouse of the scoping person's parent and not the scoping person's natural parent.
   */
  StepParent: v3_RoleCode_StepParent,
  /**
   * The scoper and player were carried in the same womb and shared common biological parents.
   */
  Twin: v3_RoleCode_Twin,
};
const participantrequired_InformationOnly: Coding = {
  code: 'information-only',
  display: 'Information Only',
  system: 'http://hl7.org/fhir/participantrequired',
};
const participantrequired_Optional: Coding = {
  code: 'optional',
  display: 'Optional',
  system: 'http://hl7.org/fhir/participantrequired',
};
const participantrequired_Required: Coding = {
  code: 'required',
  display: 'Required',
  system: 'http://hl7.org/fhir/participantrequired',
};
/**
 * Is the Participant required to attend the appointment.
 */
export const Participantrequired = {
  /**
   * The participant is excluded from the appointment, and might not be informed of the appointment taking place. (Appointment is about them, not for them - such as 2 doctors discussing results about a patient's test).
   */
  InformationOnly: participantrequired_InformationOnly,
  /**
   * The participant may optionally attend the appointment.
   */
  Optional: participantrequired_Optional,
  /**
   * The participant is required to attend the appointment.
   */
  Required: participantrequired_Required,
};
const v3_RoleCode_CitizenRoleType: Coding = {
  code: '_CitizenRoleType',
  display: 'CitizenRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const dicom_dcim_Application: Coding = {
  code: '110150',
  display: 'Application',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_ApplicationLauncher: Coding = {
  code: '110151',
  display: 'Application Launcher',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_DestinationRoleID: Coding = {
  code: '110152',
  display: 'Destination Role ID',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_SourceRoleID: Coding = {
  code: '110153',
  display: 'Source Role ID',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_DestinationMedia: Coding = {
  code: '110154',
  display: 'Destination Media',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const dicom_dcim_SourceMedia: Coding = {
  code: '110155',
  display: 'Source Media',
  system: 'http://dicom.nema.org/resources/ontology/DCM',
};
const v3_RoleClass_Affiliate: Coding = {
  code: 'AFFL',
  display: 'affiliate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Agent: Coding = {
  code: 'AGNT',
  display: 'agent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_AssignedEntity: Coding = {
  code: 'ASSIGNED',
  display: 'assigned entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_ParticipationFunction_CaregiverInformationReceiver: Coding = {
  code: 'AUCG',
  display: 'caregiver information receiver',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_LegitimateRelationshipInformationReceiver: Coding = {
  code: 'AULR',
  display: 'legitimate relationship information receiver',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationType_AuthorOriginator: Coding = {
  code: 'AUT',
  display: 'author (originator)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const extra_security_role_type_AuthorizationServer: Coding = {
  code: 'authserver',
  display: 'authorization server',
  system: 'http://terminology.hl7.org/CodeSystem/extra-security-role-type',
};
const v3_ParticipationFunction_CareTeamInformationReceiver: Coding = {
  code: 'AUTM',
  display: 'care team information receiver',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_WorkAreaInformationReceiver: Coding = {
  code: 'AUWA',
  display: 'work area information receiver',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_RoleCode_AsylumSeeker: Coding = {
  code: 'CAS',
  display: 'asylum seeker',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SingleMinorAsylumSeeker: Coding = {
  code: 'CASM',
  display: 'single minor asylum seeker',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleClass_Claimant: Coding = {
  code: 'CLAIM',
  display: 'claimant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleCode_Classifier: Coding = {
  code: 'CLASSIFIER',
  display: 'classifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_National: Coding = {
  code: 'CN',
  display: 'national',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NonCountryMemberWithoutResidencePermit: Coding = {
  code: 'CNRP',
  display: 'non-country member without residence permit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NonCountryMemberMinorWithoutResidencePermit: Coding = {
  code: 'CNRPM',
  display: 'non-country member minor without residence permit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Consenter: Coding = {
  code: 'CONSENTER',
  display: 'consenter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ConsentWitness: Coding = {
  code: 'CONSWIT',
  display: 'consent witness',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_CoParticipant: Coding = {
  code: 'COPART',
  display: 'co-participant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleClass_CoveredParty: Coding = {
  code: 'COVPTY',
  display: 'covered party',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleCode_PermitCardApplicant: Coding = {
  code: 'CPCA',
  display: 'permit card applicant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NonCountryMemberWithResidencePermit: Coding = {
  code: 'CRP',
  display: 'non-country member with residence permit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NonCountryMemberMinorWithResidencePermit: Coding = {
  code: 'CRPM',
  display: 'non-country member minor with residence permit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_ParticipationType_Custodian: Coding = {
  code: 'CST',
  display: 'custodian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const extra_security_role_type_DataCollector: Coding = {
  code: 'datacollector',
  display: 'data collector',
  system: 'http://terminology.hl7.org/CodeSystem/extra-security-role-type',
};
const extra_security_role_type_DataProcessor: Coding = {
  code: 'dataprocessor',
  display: 'data processor',
  system: 'http://terminology.hl7.org/CodeSystem/extra-security-role-type',
};
const extra_security_role_type_DataSubject: Coding = {
  code: 'datasubject',
  display: 'data subject',
  system: 'http://terminology.hl7.org/CodeSystem/extra-security-role-type',
};
const v3_RoleCode_Declassifier: Coding = {
  code: 'DECLASSIFIER',
  display: 'declassifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Delegatee: Coding = {
  code: 'DELEGATEE',
  display: 'delegatee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Delegator: Coding = {
  code: 'DELEGATOR',
  display: 'delegator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleClass_Dependent: Coding = {
  code: 'DEPEN',
  display: 'dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleCode_Downgrader: Coding = {
  code: 'DOWNGRDER',
  display: 'downgrader',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_DurablePowerOfAttorney: Coding = {
  code: 'DPOWATT',
  display: 'durable power of attorney',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleClass_EmergencyContact: Coding = {
  code: 'ECON',
  display: 'emergency contact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Employee: Coding = {
  code: 'EMP',
  display: 'employee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleCode_ExecutorOfEstate: Coding = {
  code: 'EXCEST',
  display: 'executor of estate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Grantee: Coding = {
  code: 'GRANTEE',
  display: 'grantee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Grantor: Coding = {
  code: 'GRANTOR',
  display: 'grantor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Guarantor: Coding = {
  code: 'GT',
  display: 'Guarantor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_GuardianAdLidem: Coding = {
  code: 'GUADLTM',
  display: 'guardian ad lidem',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleClass_Guardian: Coding = {
  code: 'GUARD',
  display: 'guardian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleCode_HealthcarePowerOfAttorney: Coding = {
  code: 'HPOWATT',
  display: 'healthcare power of attorney',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const extra_security_role_type_HumanUser: Coding = {
  code: 'humanuser',
  display: 'human user',
  system: 'http://terminology.hl7.org/CodeSystem/extra-security-role-type',
};
const v3_ParticipationType_Informant: Coding = {
  code: 'INF',
  display: 'informant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_RoleCode_Interpreter: Coding = {
  code: 'INTPRTER',
  display: 'interpreter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleClass_InvestigationSubject: Coding = {
  code: 'INVSBJ',
  display: 'Investigation Subject',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_ParticipationType_InformationRecipient: Coding = {
  code: 'IRCP',
  display: 'information recipient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_LegalAuthenticator: Coding = {
  code: 'LA',
  display: 'legal authenticator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_RoleClass_NamedInsured: Coding = {
  code: 'NAMED',
  display: 'named insured',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_NextOfKin: Coding = {
  code: 'NOK',
  display: 'next of kin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_NotaryPublic: Coding = {
  code: 'NOT',
  display: 'notary public',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Patient: Coding = {
  code: 'PAT',
  display: 'patient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleCode_PowerOfAttorney: Coding = {
  code: 'POWATT',
  display: 'power of attorney',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_ParticipationFunction_AuthorizedProviderMaskingAuthor: Coding = {
  code: 'PROMSK',
  display: 'authorized provider masking author',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_RoleClass_HealthcareProvider: Coding = {
  code: 'PROV',
  display: 'healthcare provider',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleCode_ResponsibleParty: Coding = {
  code: 'RESPRSN',
  display: 'responsible party',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SpecialPowerOfAttorney: Coding = {
  code: 'SPOWATT',
  display: 'special power of attorney',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_ParticipationType_Tracker: Coding = {
  code: 'TRC',
  display: 'tracker',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Witness: Coding = {
  code: 'WIT',
  display: 'witness',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
/**
 * This FHIR value set is comprised of Actor participation Type codes, which can be used to value FHIR agents, actors, and other role         elements. The FHIR Actor participation type value set is based on    DICOM Audit Message, C402;   ASTM Standard, E1762-95 [2013]; selected codes and          derived actor roles from HL7 RoleClass OID 2.16.840.1.113883.5.110;    HL7 Role Code 2.16.840.1.113883.5.111, including AgentRoleType;          HL7 ParticipationType OID: 2.16.840.1.113883.5.90; and    HL7 ParticipationFunction codes OID: 2.16.840.1.113883.5.88.           This value set includes, by reference, role codes from external code systems: NUCC Health Care Provider Taxonomy OID: 2.16.840.1.113883.6.101;          North American Industry Classification System [NAICS]OID: 2.16.840.1.113883.6.85; IndustryClassificationSystem 2.16.840.1.113883.1.11.16039;          and US Census Occupation Code OID: 2.16.840.1.113883.6.243 for relevant recipient or custodian codes not included in this value set.            If no source is indicated in the definition comments, then these are example FHIR codes.
 */
export const ParticipationRoleType = {
  /**
   * A role type used to qualify a person's legal status within a country or nation.
   */
  CitizenRoleType_v3_RoleCode: v3_RoleCode_CitizenRoleType,
  /**
   * Audit participant role ID of software application
   */
  Application_dicom_dcim: dicom_dcim_Application,
  /**
   * Audit participant role ID of software application launcher, i.e., the entity that started or stopped an application
   */
  ApplicationLauncher_dicom_dcim: dicom_dcim_ApplicationLauncher,
  /**
   * Audit participant role ID of the receiver of data
   */
  DestinationRoleID_dicom_dcim: dicom_dcim_DestinationRoleID,
  /**
   * Audit participant role ID of the sender of data
   */
  SourceRoleID_dicom_dcim: dicom_dcim_SourceRoleID,
  /**
   * Audit participant role ID of media receiving data during an export
   */
  DestinationMedia_dicom_dcim: dicom_dcim_DestinationMedia,
  /**
   * Audit participant role ID of media providing data during an import
   */
  SourceMedia_dicom_dcim: dicom_dcim_SourceMedia,
  /**
   * Player of the Affiliate role has a business/professional relationship with scoper.  Player and scoper may be persons or organization.  The Affiliate relationship does not imply membership in a group, nor does it exist for resource scheduling purposes.
   *
   *
   *                            Example: A healthcare provider is affiliated with another provider as a business associate.
   */
  Affiliate_v3_RoleClass: v3_RoleClass_Affiliate,
  /**
   * An entity (player) that acts or is authorized to act on behalf of another entity (scoper).
   */
  Agent_v3_RoleClass: v3_RoleClass_Agent,
  /**
   * A person who has corrected, edited, or amended pre-existing information.
   */
  Amender_contract_signer_type: contract_signer_type_Amender,
  /**
   * An agent role in which the agent is an Entity acting in the employ of an organization.  The focus is on functional role on behalf of the organization, unlike the Employee role where the focus is on the 'Human Resources' relationship between the employee and the organization.
   */
  AssignedEntity_v3_RoleClass: v3_RoleClass_AssignedEntity,
  /**
   * Description:Caregiver authorized to receive patient health information.
   */
  CaregiverInformationReceiver_v3_ParticipationFunction: v3_ParticipationFunction_CaregiverInformationReceiver,
  /**
   * Description:Provider with legitimate relationship authorized to receive patient health information.
   */
  LegitimateRelationshipInformationReceiver_v3_ParticipationFunction: v3_ParticipationFunction_LegitimateRelationshipInformationReceiver,
  /**
   * Definition: A party that originates the Act and therefore has responsibility for the information given in the Act and ownership of this Act.
   *
   *
   *                            Example: the report writer, the person writing the act definition, the guideline author, the placer of an order, the EKG cart (device) creating a report etc. Every Act should have an author. Authorship is regardless of mood always actual authorship.
   *
   *                         Examples of such policies might include:
   *
   *
   *
   *                               The author and anyone they explicitly delegate may update the report;
   *
   *
   *
   *                               All administrators within the same clinic may cancel and reschedule appointments created by other administrators within that clinic;
   *
   *
   *
   *                         A party that is neither an author nor a party who is extended authorship maintenance rights by policy, may only amend, reverse, override, replace, or follow up in other ways on this Act, whereby the Act remains intact and is linked to another Act authored by that other party.
   */
  AuthorOriginator_v3_ParticipationType: v3_ParticipationType_AuthorOriginator,
  /**
   * An entity providing authorization services to enable the electronic sharing of health-related information based on resource owner's preapproved permissions. For example, an UMA Authorization Server[UMA]
   */
  AuthorizationServer_extra_security_role_type: extra_security_role_type_AuthorizationServer,
  /**
   * Description:Member of care team authorized to receive patient health information.
   */
  CareTeamInformationReceiver_v3_ParticipationFunction: v3_ParticipationFunction_CareTeamInformationReceiver,
  /**
   * Description:Entities within specified work area authorized to receive patient health information.
   */
  WorkAreaInformationReceiver_v3_ParticipationFunction: v3_ParticipationFunction_WorkAreaInformationReceiver,
  /**
   * A person who has fled his or her home country to find a safe place elsewhere.
   */
  AsylumSeeker_v3_RoleCode: v3_RoleCode_AsylumSeeker,
  /**
   * A person who is someone of below legal age who has fled his or her home country, without his or her parents, to find a safe place elsewhere at time of categorization.
   */
  SingleMinorAsylumSeeker_v3_RoleCode: v3_RoleCode_SingleMinorAsylumSeeker,
  /**
   * Description: A role played by a party making a claim for coverage under a policy or program.  A claimant must be either a person or organization, or a group of persons or organizations.  A claimant is not a named insured or a program eligible.
   *
   *
   *                            Discussion: With respect to liability insurance such as property and casualty insurance, a claimant must file a claim requesting indemnification for a loss that the claimant considers covered under the policy of a named insured.  The claims adjuster for the policy underwriter will review the claim to determine whether the loss meets the benefit coverage criteria under a policy, and base any indemnification or coverage payment on that review.  If a third party is liable in whole or part for the loss, the underwriter may pursue third party liability recovery.  A claimant may be involved in civil or criminal legal proceedings involving claims against a defendant party that is indemnified by an insurance policy or to protest the finding of a claims adjustor. With respect to life insurance, a beneficiary designated by a named insured becomes a claimant of the proceeds of coverage, as in the case of a life insurance policy.  However, a claimant for coverage under life insurance is not necessarily a designated beneficiary.
   *
   *
   *                            Note: A claimant is not a named insured.  However, a named insured may make a claim under a policy, e.g., an insured driver may make a claim for an injury under his or her comprehensive automobile insurance policy.  Similarly, a program eligible may make a claim under program, e.g., an unemployed worker may claim benefits under an unemployment insurance program, but parties playing these covered party role classes are not, for purposes of this vocabulary and in an effort to clearly distinguish role classes, considered claimants.
   *
   *                         In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that either a named insured or an individual insured has filed a claim for a loss.  In the case of a program eligible, a role type code INJWKR (injured worker) subtypes the class to indicate that the covered party in a workers compensation program is an injured worker, and as such, has filed a "claim" under the program for benefits.  Likewise, a covered role type code UNEMP (unemployed worker) subtypes the program eligible class to indicate that the covered party in an unemployment insurance program has filed a claim for unemployment benefits.
   *
   *
   *                            Example: A claimant under automobile policy that is not the named insured.
   */
  Claimant_v3_RoleClass: v3_RoleClass_Claimant,
  /**
   * An individual authorized to assign an original classification to information, including compilations of unclassified information, based on a determination that the information requires protection against unauthorized disclosure. The individual marks the information with immutable, computable, and human readable security labels in accordance with applicable security labeling policies.  The labeling policies provide instructions on whether and if so how the security labels may be later reclassified [i.e., upgraded, downgraded, used in derivative classification, or declassified] in a manner that preserves the overridden original classification binding and provenance.
   */
  Classifier_v3_RoleCode: v3_RoleCode_Classifier,
  /**
   * A person who is legally recognized as a member of a nation or country, with associated rights and obligations.
   */
  National_v3_RoleCode: v3_RoleCode_National,
  /**
   * A foreigner who is present in a country (which is foreign to him/her) unlawfully or without the country's authorization (may be called an illegal alien).
   */
  NonCountryMemberWithoutResidencePermit_v3_RoleCode: v3_RoleCode_NonCountryMemberWithoutResidencePermit,
  /**
   * A person who is below legal age present in a country, without his or her parents, (which is foreign to him/her) unlawfully or without the country's authorization.
   */
  NonCountryMemberMinorWithoutResidencePermit_v3_RoleCode: v3_RoleCode_NonCountryMemberMinorWithoutResidencePermit,
  /**
   * The entity that co-authored content. There can be multiple co-authors of content, which may take such forms as a contract, a healthcare record entry or document, a policy, or a consent directive.
   */
  CoAuthor_contract_signer_type: contract_signer_type_CoAuthor,
  /**
   * An entity or an entity's delegatee who is the grantee in an agreement such as a consent for services, advanced directive, or a privacy consent directive in accordance with jurisdictional, organizational, or patient policy.
   */
  Consenter_v3_RoleCode: v3_RoleCode_Consenter,
  /**
   * An entity which has witnessed and attests to observing another entity being counseled about an agreement such as a consent for services, advanced directive, or a privacy consent directive.
   */
  ConsentWitness_v3_RoleCode: v3_RoleCode_ConsentWitness,
  /**
   * A person or an organization that provides or receives information regarding another entity. Examples; patient NOK and emergency contacts; guarantor contact; employer contact.
   */
  Contact_contract_signer_type: contract_signer_type_Contact,
  /**
   * An entity which participates in the generation of and attest to veracity of content, but is not an author or coauthor. For example a surgeon who is required by institutional, regulatory, or legal rules to sign an operative report, but who was not involved in the authorship of that report.
   */
  CoParticipant_v3_RoleCode: v3_RoleCode_CoParticipant,
  /**
   * A role class played by a person who receives benefit coverage under the terms of a particular insurance policy.  The underwriter of that policy is the scoping entity.  The covered party receives coverage because of some contractual or other relationship with the holder of that policy.
   *
   *
   *                            Discussion:This reason for coverage is captured in 'Role.code' and a relationship link with type code of indirect authority should be included using the policy holder role as the source, and the covered party role as the target.
   *
   *                         Note that a particular policy may cover several individuals one of whom may be, but need not be, the policy holder.  Thus the notion of covered party is a role that is distinct from that of the policy holder.
   */
  CoveredParty_v3_RoleClass: v3_RoleClass_CoveredParty,
  /**
   * A non-country member admitted to the territory of a nation or country as a non-resident for an explicit purpose.
   */
  PermitCardApplicant_v3_RoleCode: v3_RoleCode_PermitCardApplicant,
  /**
   * A foreigner who is a resident of the country but does not have citizenship.
   */
  NonCountryMemberWithResidencePermit_v3_RoleCode: v3_RoleCode_NonCountryMemberWithResidencePermit,
  /**
   * A person who is a resident below legal age of the country without his or her parents and does not have citizenship.
   */
  NonCountryMemberMinorWithResidencePermit_v3_RoleCode: v3_RoleCode_NonCountryMemberMinorWithResidencePermit,
  /**
   * An entity (person, organization or device) that is in charge of maintaining the information of this act (e.g., who maintains the report or the master service catalog item, etc.).
   */
  Custodian_v3_ParticipationType: v3_ParticipationType_Custodian,
  /**
   * An entity that collects information over which the data subject may have certain rights under policy or law to control that information's management and distribution by data collectors, including the right to access, retrieve, distribute, or delete that information.
   */
  DataCollector_extra_security_role_type: extra_security_role_type_DataCollector,
  /**
   * An entity that processes collected information over which the data subject may have certain rights under policy or law to control that information's management and distribution by data processors, including the right to access, retrieve, distribute, or delete that information.
   */
  DataProcessor_extra_security_role_type: extra_security_role_type_DataProcessor,
  /**
   * A person whose personal information is collected or processed, and who may have certain rights under policy or law to control that information's management and distribution by data collectors or processors, including the right to access, retrieve, distribute, or delete that information.
   */
  DataSubject_extra_security_role_type: extra_security_role_type_DataSubject,
  /**
   * An individual which is authorized to declassify information based on a determination that the information no longer requires protection against unauthorized disclosure.  The individual marks the information being declassified using computable and human readable security labels indicating that this is copy of previously classified information is unclassified in accordance with applicable security labeling policies.  The labeling policies provide instructions on whether and if so how the security labels may be later reclassified [i.e., upgraded or used in derivative classification] in a manner that preserves the overridden original classification binding and provenance.
   */
  Declassifier_v3_RoleCode: v3_RoleCode_Declassifier,
  /**
   * A party to whom some right or authority is granted by a delegator.
   */
  Delegatee_v3_RoleCode: v3_RoleCode_Delegatee,
  /**
   * A party that grants all or some portion its right or authority to another party.
   */
  Delegator_v3_RoleCode: v3_RoleCode_Delegator,
  /**
   * Description: A role played by a person covered under a policy or program based on an association with a subscriber, which is recognized by the policy holder.
   *
   *
   *                            Note:  The party playing the role of a dependent is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a dependent may make a claim under a policy, e.g., a dependent under a health insurance policy may become the claimant for coverage under that policy for wellness examines or if injured and there is no liable third party.  In the case of a dependent making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that the dependent has filed a claim for services covered under the health insurance policy.
   *
   *
   *                            Example: The dependent has an association with the subscriber such as a financial dependency or personal relationship such as that of a spouse, or a natural or adopted child.  The policy holder may be required by law to recognize certain associations or may have discretion about the associations.  For example, a policy holder may dictate the criteria for the dependent status of adult children who are students, such as requiring full time enrollment, or may recognize domestic partners as dependents.  Under certain circumstances, the dependent may be under the indirect authority of a responsible party acting as a surrogate for the subscriber, for example, if the subscriber is differently abled or deceased, a guardian ad Lidem or estate executor may be appointed to assume the subscriberaTMs legal standing in the relationship with the dependent.
   */
  Dependent_v3_RoleClass: v3_RoleClass_Dependent,
  /**
   * An individual authorized to lower the classification level of labeled content and provide rationale for doing so as directed by a classification guide.
   */
  Downgrader_v3_RoleCode: v3_RoleCode_Downgrader,
  /**
   * A relationship between two people in which one person authorizes another, usually a family member or relative, to act for him or her in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts that is often limited in the kinds of powers that can be assigned.  Unlike ordinary powers of attorney, durable powers can survive for long periods of time, and again, unlike standard powers of attorney, durable powers can continue after incompetency.
   */
  DurablePowerOfAttorney_v3_RoleCode: v3_RoleCode_DurablePowerOfAttorney,
  /**
   * An entity to be contacted in the event of an emergency.
   */
  EmergencyContact_v3_RoleClass: v3_RoleClass_EmergencyContact,
  /**
   * A relationship between a person or organization and a person or organization formed for the purpose of exchanging work for compensation.  The purpose of the role is to identify the type of relationship the employee has to the employer, rather than the nature of the work actually performed.  (Contrast with AssignedEntity.)
   */
  Employee_v3_RoleClass: v3_RoleClass_Employee,
  /**
   * A person who attests to observing an occurrence.  For example, the witness has observed a procedure and is attesting to this fact.
   */
  EventWitness_contract_signer_type: contract_signer_type_EventWitness,
  /**
   * The role played by a person acting as the estate executor for a deceased subscriber or policyholder who was the responsible party
   */
  ExecutorOfEstate_v3_RoleCode: v3_RoleCode_ExecutorOfEstate,
  /**
   * An entity which accepts certain rights or authority from a grantor.
   */
  Grantee_v3_RoleCode: v3_RoleCode_Grantee,
  /**
   * An entity which agrees to confer certain rights or authority to a grantee.
   */
  Grantor_v3_RoleCode: v3_RoleCode_Grantor,
  /**
   * An individual or organization that makes or gives a promise, assurance, pledge to pay or has paid the healthcare service provider.
   */
  Guarantor_v3_RoleCode: v3_RoleCode_Guarantor,
  /**
   * The role played by a person appointed by the court to look out for the best interests of a minor child during the course of legal proceedings.
   */
  GuardianAdLidem_v3_RoleCode: v3_RoleCode_GuardianAdLidem,
  /**
   * Guardian of a ward
   */
  Guardian_v3_RoleClass: v3_RoleClass_Guardian,
  /**
   * A relationship between two people in which one person authorizes another to act for him or her in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts that continues (by its terms) to be effective even though the grantor has become mentally incompetent after signing the document.
   */
  HealthcarePowerOfAttorney_v3_RoleCode: v3_RoleCode_HealthcarePowerOfAttorney,
  /**
   * The human user that has participated.
   */
  HumanUser_extra_security_role_type: extra_security_role_type_HumanUser,
  /**
   * A source of reported information (e.g., a next of kin who answers questions about the patient's history).  For history questions, the patient is logically an informant, yet the informant of history questions is implicitly the subject.
   */
  Informant_v3_ParticipationType: v3_ParticipationType_Informant,
  /**
   * An entity which converts spoken or written language into the language of key participants in an event such as when a provider is obtaining a patient's consent to treatment or permission to disclose information.
   */
  Interpreter_v3_RoleCode: v3_RoleCode_Interpreter,
  /**
   * An entity that is the subject of an investigation. This role is scoped by the party responsible for the investigation.
   */
  InvestigationSubject_v3_RoleClass: v3_RoleClass_InvestigationSubject,
  /**
   * A party, who may or should receive or who has recieved the Act or subsequent or derivative information of that Act. Information recipient is inert, i.e., independent of mood." Rationale: this is a generalization of a too diverse family that the definition can't be any more specific, and the concept is abstract so one of the specializations should be used.
   */
  InformationRecipient_v3_ParticipationType: v3_ParticipationType_InformationRecipient,
  /**
   * A verifier who legally authenticates the accuracy of an act. An example would be a staff physician who sees a patient and dictates a note, then later signs it. Their signature constitutes a legal authentication.
   */
  LegalAuthenticator_v3_ParticipationType: v3_ParticipationType_LegalAuthenticator,
  /**
   * Description: A role played by a party to an insurance policy to which the insurer agrees to indemnify for losses, provides benefits for, or renders services.  A named insured may be either a person, non-person living subject, or an organization, or a group of persons, non-person living subjects, or organizations.
   *
   *
   *                            Discussion: The coded concept NAMED should not be used where a more specific child concept in this Specializable value set applies.  In some cases, the named insured may not be the policy holder, e.g., where a policy holder purchases life insurance policy in which another party is the named insured and the policy holder is the beneficiary of the policy.
   *
   *
   *                            Note: The party playing the role of a named insured is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a named insured may make a claim under a policy, e.g., e.g., a party that is the named insured and policy holder under a comprehensive automobile insurance policy may become the claimant for coverage under that policy e.g., if injured in an automobile accident and there is no liable third party.  In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that a named insured has filed a claim for a loss.
   *
   *
   *                            Example: The named insured under a comprehensive automobile, disability, or property and casualty policy that is the named insured and may or may not be the policy holder.
   */
  NamedInsured_v3_RoleClass: v3_RoleClass_NamedInsured,
  /**
   * An individual designated for notification as the next of kin for a given entity.
   */
  NextOfKin_v3_RoleClass: v3_RoleClass_NextOfKin,
  /**
   * notary public
   */
  NotaryPublic_v3_RoleClass: v3_RoleClass_NotaryPublic,
  /**
   * A Role of a LivingSubject (player) as an actual or potential recipient of health care services from a healthcare provider organization (scoper).
   *
   *
   *                            Usage Note: Communication about relationships between patients and specific healthcare practitioners (people) is not done via scoper.  Instead this is generally done using the CareProvision act.  This allows linkage between patient and a particular healthcare practitioner role and also allows description of the type of care involved in the relationship.
   */
  Patient_v3_RoleClass: v3_RoleClass_Patient,
  /**
   * A relationship between two people in which one person authorizes another to act for him in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts.
   */
  PowerOfAttorney_v3_RoleCode: v3_RoleCode_PowerOfAttorney,
  /**
   * An entity that is the primary or sole author of information content.  In the healthcare context, there can be only one primary author of health information content in a record entry or document.
   */
  PrimaryAuthor_contract_signer_type: contract_signer_type_PrimaryAuthor,
  /**
   * Definition:Provider authorized to mask information to protect the patient, a third party, or to ensure that the provider has consulted with the patient prior to release of this information.
   */
  AuthorizedProviderMaskingAuthor_v3_ParticipationFunction: v3_ParticipationFunction_AuthorizedProviderMaskingAuthor,
  /**
   * An Entity (player) that is authorized to provide health care services by some authorizing agency (scoper).
   */
  HealthcareProvider_v3_RoleClass: v3_RoleClass_HealthcareProvider,
  /**
   * The role played by a party who has legal responsibility for another party.
   */
  ResponsibleParty_v3_RoleCode: v3_RoleCode_ResponsibleParty,
  /**
   * A person, device, or algorithm that has used approved criteria for filtered data for inclusion into the patient record.  Examples: (1) a medical records clerk who scans a document for inclusion in the medical record, enters header information, or catalogues and classifies the data, or a combination thereof; (2) a gateway that receives data from another computer system and interprets that data or changes its format, or both, before entering it into the patient record.
   */
  Reviewer_contract_signer_type: contract_signer_type_Reviewer,
  /**
   * An automated data source that generates a signature along with content. Examples: (1) the signature for an image that is generated by a device for inclusion in the patient record; (2) the signature for an ECG derived by an ECG system for inclusion in the patient record; (3) the data from a biomedical monitoring device or system that is for inclusion in the patient record.
   */
  Source_contract_signer_type: contract_signer_type_Source,
  /**
   * A relationship between two people in which one person authorizes another to act for him or her in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts that is often limited in the kinds of powers that can be assigned.
   */
  SpecialPowerOfAttorney_v3_RoleCode: v3_RoleCode_SpecialPowerOfAttorney,
  /**
   * An entity entering the data into the originating system. This includes the transcriptionist for dictated text transcribed into electronic form.
   */
  Transcriber_contract_signer_type: contract_signer_type_Transcriber,
  /**
   * A secondary information recipient, who receives copies (e.g., a primary care provider receiving copies of results as ordered by specialist).
   */
  Tracker_v3_ParticipationType: v3_ParticipationType_Tracker,
  /**
   * A person who validates a health information document for inclusion in the patient record. For example, a medical student or resident is credentialed to perform history or physical examinations and to write progress notes. The attending physician signs the history and physical examination to validate the entry for inclusion in the patient's medical record.
   */
  Validator_contract_signer_type: contract_signer_type_Validator,
  /**
   * A person who asserts the correctness and appropriateness of an act or the recording of the act, and is accountable for the assertion that the act or the recording of the act complies with jurisdictional or organizational policy. For example, a physician is required to countersign a verbal order that has previously been recorded in the medical record by a registered nurse who has carried out the verbal order.
   */
  Verifier_contract_signer_type: contract_signer_type_Verifier,
  /**
   * Only with service events.  A person witnessing the action happening without doing anything.  A witness is not necessarily aware, much less approves of anything stated in the service event.  Example for a witness is students watching an operation or an advanced directive witness.
   */
  Witness_v3_ParticipationType: v3_ParticipationType_Witness,
};
const participationstatus_Accepted: Coding = {
  code: 'accepted',
  display: 'Accepted',
  system: 'http://hl7.org/fhir/participationstatus',
};
const participationstatus_Declined: Coding = {
  code: 'declined',
  display: 'Declined',
  system: 'http://hl7.org/fhir/participationstatus',
};
const participationstatus_NeedsAction: Coding = {
  code: 'needs-action',
  display: 'Needs Action',
  system: 'http://hl7.org/fhir/participationstatus',
};
const participationstatus_Tentative: Coding = {
  code: 'tentative',
  display: 'Tentative',
  system: 'http://hl7.org/fhir/participationstatus',
};
/**
 * The Participation status of an appointment.
 */
export const Participationstatus = {
  /**
   * The participant has accepted the appointment.
   */
  Accepted: participationstatus_Accepted,
  /**
   * The participant has declined the appointment and will not participate in the appointment.
   */
  Declined: participationstatus_Declined,
  /**
   * The participant needs to indicate if they accept the appointment by changing this status to one of the other statuses.
   */
  NeedsAction: participationstatus_NeedsAction,
  /**
   * The participant has  tentatively accepted the appointment. This could be automatically created by a system and requires further processing before it can be accepted. There is no commitment that attendance will occur.
   */
  Tentative: participationstatus_Tentative,
};
const payeetype_Provider: Coding = {
  code: 'other',
  display: 'Provider',
  system: 'http://terminology.hl7.org/CodeSystem/payeetype',
};
const payeetype_Provider_2: Coding = {
  code: 'provider',
  display: 'Provider',
  system: 'http://terminology.hl7.org/CodeSystem/payeetype',
};
const payeetype_Subscriber: Coding = {
  code: 'subscriber',
  display: 'Subscriber',
  system: 'http://terminology.hl7.org/CodeSystem/payeetype',
};
/**
 * This value set includes sample Payee Type codes.
 */
export const Payeetype = {
  /**
   * Any benefit payable will be paid to a third party such as a guarrantor.
   */
  Provider: payeetype_Provider,
  /**
   * Any benefit payable will be paid to the provider (Assignment of Benefit).
   */
  Provider_2: payeetype_Provider_2,
  /**
   * The subscriber (policy holder) will be reimbursed.
   */
  Subscriber: payeetype_Subscriber,
};
const payment_adjustment_reason_PriorPaymentReversal: Coding = {
  code: 'a001',
  display: 'Prior Payment Reversal',
  system: 'http://terminology.hl7.org/CodeSystem/payment-adjustment-reason',
};
const payment_adjustment_reason_PriorOverpayment: Coding = {
  code: 'a002',
  display: 'Prior Overpayment',
  system: 'http://terminology.hl7.org/CodeSystem/payment-adjustment-reason',
};
/**
 * This value set includes smattering of Payment Adjustment Reason codes.
 */
export const PaymentAdjustmentReason = {
  /**
   * Prior Payment Reversal
   */
  PriorPaymentReversal: payment_adjustment_reason_PriorPaymentReversal,
  /**
   * Prior Overpayment
   */
  PriorOverpayment: payment_adjustment_reason_PriorOverpayment,
};
const payment_status_Cleared: Coding = {
  code: 'cleared',
  display: 'Cleared',
  system: 'http://terminology.hl7.org/CodeSystem/paymentstatus',
};
const payment_status_Paid: Coding = {
  code: 'paid',
  display: 'Paid',
  system: 'http://terminology.hl7.org/CodeSystem/paymentstatus',
};
/**
 * This value set includes a sample set of Payment Status codes.
 */
export const PaymentStatus = {
  /**
   * The payment has been received by the payee.
   */
  Cleared: payment_status_Cleared,
  /**
   * The payment has been sent physically or electronically.
   */
  Paid: payment_status_Paid,
};
const payment_type_Adjustment: Coding = {
  code: 'adjustment',
  display: 'Adjustment',
  system: 'http://terminology.hl7.org/CodeSystem/payment-type',
};
const payment_type_Advance: Coding = {
  code: 'advance',
  display: 'Advance',
  system: 'http://terminology.hl7.org/CodeSystem/payment-type',
};
const payment_type_Payment: Coding = {
  code: 'payment',
  display: 'Payment',
  system: 'http://terminology.hl7.org/CodeSystem/payment-type',
};
/**
 * This value set includes sample Payment Type codes.
 */
export const PaymentType = {
  /**
   * The amount is an adjustment regarding claims already paid.
   */
  Adjustment: payment_type_Adjustment,
  /**
   * The amount is an advance against future claims.
   */
  Advance: payment_type_Advance,
  /**
   * The amount is partial or complete settlement of the amounts due.
   */
  Payment: payment_type_Payment,
};
const v3_ParticipationType_Authenticator: Coding = {
  code: 'AUTHEN',
  display: 'authenticator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_DataEntryPerson: Coding = {
  code: 'ENT',
  display: 'data entry person',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_ResponsibleParty: Coding = {
  code: 'RESP',
  display: 'responsible party',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Transcriber: Coding = {
  code: 'TRANS',
  display: 'Transcriber',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Verifier: Coding = {
  code: 'VRF',
  display: 'verifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
/**
 * The types of involvement of the performer in the Event.
 */
export const PerformerFunction = {
  /**
   * The practitioner that has responsibility for overseeing a patient's care during a patient encounter.
   */
  Attender: v3_ParticipationType_Attender,
  /**
   * Definition: A party that originates the Act and therefore has responsibility for the information given in the Act and ownership of this Act.
   *
   *
   *                            Example: the report writer, the person writing the act definition, the guideline author, the placer of an order, the EKG cart (device) creating a report etc. Every Act should have an author. Authorship is regardless of mood always actual authorship.
   *
   *                         Examples of such policies might include:
   *
   *
   *
   *                               The author and anyone they explicitly delegate may update the report;
   *
   *
   *
   *                               All administrators within the same clinic may cancel and reschedule appointments created by other administrators within that clinic;
   *
   *
   *
   *                         A party that is neither an author nor a party who is extended authorship maintenance rights by policy, may only amend, reverse, override, replace, or follow up in other ways on this Act, whereby the Act remains intact and is linked to another Act authored by that other party.
   */
  AuthorOriginator: v3_ParticipationType_AuthorOriginator,
  /**
   * A verifier who attests to the accuracy of an act, but who does not have privileges to legally authenticate the act. An example would be a resident physician who sees a patient and dictates a note, then later signs it. Their signature constitutes an authentication.
   */
  Authenticator: v3_ParticipationType_Authenticator,
  /**
   * An advisor participating in the service by performing evaluations and making recommendations.
   */
  Consultant: v3_ParticipationType_Consultant,
  /**
   * A person entering the data into the originating system.  The data entry person is collected optionally for internal quality control purposes.  This includes the transcriptionist for dictated text.
   */
  DataEntryPerson: v3_ParticipationType_DataEntryPerson,
  /**
   * A source of reported information (e.g., a next of kin who answers questions about the patient's history).  For history questions, the patient is logically an informant, yet the informant of history questions is implicitly the subject.
   */
  Informant: v3_ParticipationType_Informant,
  /**
   * A verifier who legally authenticates the accuracy of an act. An example would be a staff physician who sees a patient and dictates a note, then later signs it. Their signature constitutes a legal authentication.
   */
  LegalAuthenticator: v3_ParticipationType_LegalAuthenticator,
  /**
   * Indicates that the target of the participation is involved in some manner in the act, but does not qualify how.
   */
  Participation: v3_ParticipationType_Participation,
  /**
   * The principal or primary performer of the act.
   */
  PrimaryPerformer: v3_ParticipationType_PrimaryPerformer,
  /**
   * The person or organization that has primary responsibility for the act.  The responsible party is not necessarily present in an action, but is accountable for the action through the power to delegate, and the duty to review actions with the performing actor after the fact.  This responsibility may be ethical, legal, contractual, fiscal, or fiduciary in nature.
   *
   *
   *                            Example: A person who is the head of a biochemical laboratory; a sponsor for a policy or government program.
   */
  ResponsibleParty: v3_ParticipationType_ResponsibleParty,
  /**
   * A person assisting in an act through his substantial presence and involvement   This includes: assistants, technicians, associates, or whatever the job titles may be.
   */
  SecondaryPerformer: v3_ParticipationType_SecondaryPerformer,
  /**
   * An entity entering the data into the originating system. The data entry entity is collected optionally for internal quality control purposes. This includes the transcriptionist for dictated text transcribed into electronic form.
   */
  Transcriber: v3_ParticipationType_Transcriber,
  /**
   * A person who verifies the correctness and appropriateness of the service (plan, order, event, etc.) and hence takes on accountability.
   */
  Verifier: v3_ParticipationType_Verifier,
  /**
   * Only with service events.  A person witnessing the action happening without doing anything.  A witness is not necessarily aware, much less approves of anything stated in the service event.  Example for a witness is students watching an operation or an advanced directive witness.
   */
  Witness: v3_ParticipationType_Witness,
};
const permitted_data_type_Boolean: Coding = {
  code: 'boolean',
  display: 'boolean',
  system: 'http://hl7.org/fhir/permitted-data-type',
};
const permitted_data_type_CodeableConcept: Coding = {
  code: 'CodeableConcept',
  display: 'CodeableConcept',
  system: 'http://hl7.org/fhir/permitted-data-type',
};
const permitted_data_type_DateTime: Coding = {
  code: 'dateTime',
  display: 'dateTime',
  system: 'http://hl7.org/fhir/permitted-data-type',
};
const permitted_data_type_Integer: Coding = {
  code: 'integer',
  display: 'integer',
  system: 'http://hl7.org/fhir/permitted-data-type',
};
const permitted_data_type_Period: Coding = {
  code: 'Period',
  display: 'Period',
  system: 'http://hl7.org/fhir/permitted-data-type',
};
const permitted_data_type_Quantity: Coding = {
  code: 'Quantity',
  display: 'Quantity',
  system: 'http://hl7.org/fhir/permitted-data-type',
};
const permitted_data_type_Range: Coding = {
  code: 'Range',
  display: 'Range',
  system: 'http://hl7.org/fhir/permitted-data-type',
};
const permitted_data_type_Ratio: Coding = {
  code: 'Ratio',
  display: 'Ratio',
  system: 'http://hl7.org/fhir/permitted-data-type',
};
const permitted_data_type_SampledData: Coding = {
  code: 'SampledData',
  display: 'SampledData',
  system: 'http://hl7.org/fhir/permitted-data-type',
};
const permitted_data_type_String: Coding = {
  code: 'string',
  display: 'string',
  system: 'http://hl7.org/fhir/permitted-data-type',
};
const permitted_data_type_Time: Coding = {
  code: 'time',
  display: 'time',
  system: 'http://hl7.org/fhir/permitted-data-type',
};
/**
 * Permitted data type for observation value.
 */
export const PermittedDataType = {
  /**
   * true or false.
   */
  Boolean: permitted_data_type_Boolean,
  /**
   * A coded concept from a reference terminology and/or text.
   */
  CodeableConcept: permitted_data_type_CodeableConcept,
  /**
   * A date, date-time or partial date (e.g. just year or year + month) as used in human communication.
   */
  DateTime: permitted_data_type_DateTime,
  /**
   * A signed integer.
   */
  Integer: permitted_data_type_Integer,
  /**
   * A time range defined by start and end date/time.
   */
  Period: permitted_data_type_Period,
  /**
   * A measured amount.
   */
  Quantity: permitted_data_type_Quantity,
  /**
   * A set of values bounded by low and high.
   */
  Range: permitted_data_type_Range,
  /**
   * A ratio of two Quantity values - a numerator and a denominator.
   */
  Ratio: permitted_data_type_Ratio,
  /**
   * A series of measurements taken by a device.
   */
  SampledData: permitted_data_type_SampledData,
  /**
   * A sequence of Unicode characters.
   */
  String: permitted_data_type_String,
  /**
   * A time during the day, in the format hh:mm:ss.
   */
  Time: permitted_data_type_Time,
};
const plan_definition_type_ClinicalProtocol: Coding = {
  code: 'clinical-protocol',
  display: 'Clinical Protocol',
  system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
};
const plan_definition_type_ECARule: Coding = {
  code: 'eca-rule',
  display: 'ECA Rule',
  system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
};
const plan_definition_type_OrderSet: Coding = {
  code: 'order-set',
  display: 'Order Set',
  system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
};
const plan_definition_type_WorkflowDefinition: Coding = {
  code: 'workflow-definition',
  display: 'Workflow Definition',
  system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
};
/**
 * The type of PlanDefinition.
 */
export const PlanDefinitionType = {
  /**
   * Defines a desired/typical sequence of clinical activities including preconditions, triggers and temporal relationships.
   */
  ClinicalProtocol: plan_definition_type_ClinicalProtocol,
  /**
   * A decision support rule of the form [on Event] if Condition then Action. It is intended to be a shareable, computable definition of actions that should be taken whenever some condition is met in response to a particular event or events.
   */
  ECARule: plan_definition_type_ECARule,
  /**
   * A pre-defined and approved group of orders related to a particular clinical condition (e.g. hypertension treatment and monitoring) or stage of care (e.g. hospital admission to Coronary Care Unit). An order set is used as a checklist for the clinician when managing a patient with a specific condition. It is a structured collection of orders relevant to that condition and presented to the clinician in a computerized provider order entry (CPOE) system.
   */
  OrderSet: plan_definition_type_OrderSet,
  /**
   * Defines the steps for a group of one or more systems in an event flow process along with the step constraints, sequence, pre-conditions and decision points to complete a particular objective.
   */
  WorkflowDefinition: plan_definition_type_WorkflowDefinition,
};
const v3_AddressUse_BadAddress: Coding = {
  code: 'BAD',
  display: 'bad address',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_ConfidentialAddress: Coding = {
  code: 'CONF',
  display: 'confidential address',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_Direct: Coding = {
  code: 'DIR',
  display: 'direct',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_PrimaryHome: Coding = {
  code: 'HP',
  display: 'primary home',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_VacationHome: Coding = {
  code: 'HV',
  display: 'vacation home',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_PhysicalVisitAddress: Coding = {
  code: 'PHYS',
  display: 'physical visit address',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_PostalAddress: Coding = {
  code: 'PST',
  display: 'postal address',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_Public: Coding = {
  code: 'PUB',
  display: 'public',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
/**
 * Uses of an address not included in Address.use.
 */
export const PostalAddressUse = {
  /**
   * Description: A flag indicating that the address is bad, in fact, useless.
   */
  BadAddress: v3_AddressUse_BadAddress,
  /**
   * Description: Indicates that the address is considered sensitive and should only be shared or published in accordance with organizational controls governing patient demographic information with increased sensitivity. Uses of Addresses.   Lloyd to supply more complete description.
   */
  ConfidentialAddress: v3_AddressUse_ConfidentialAddress,
  /**
   * Description: Indicates a work place address or telecommunication address that reaches the individual or organization directly without intermediaries. For phones, often referred to as a 'private line'.
   */
  Direct: v3_AddressUse_Direct,
  /**
   * Description: The primary home, to reach a person after business hours.
   */
  PrimaryHome: v3_AddressUse_PrimaryHome,
  /**
   * Description: A vacation home, to reach a person while on vacation.
   */
  VacationHome: v3_AddressUse_VacationHome,
  /**
   * Description: Used primarily to visit an address.
   */
  PhysicalVisitAddress: v3_AddressUse_PhysicalVisitAddress,
  /**
   * Description: Used to send mail.
   */
  PostalAddress: v3_AddressUse_PostalAddress,
  /**
   * Description: Indicates a work place address or telecommunication address that is a 'standard' address which may reach a reception service, mail-room, or other intermediary prior to the target entity.
   */
  Public: v3_AddressUse_Public,
};
const practitioner_role_Doctor: Coding = {
  code: 'doctor',
  display: 'Doctor',
  system: 'http://terminology.hl7.org/CodeSystem/practitioner-role',
};
const practitioner_role_ICTProfessional: Coding = {
  code: 'ict',
  display: 'ICT professional',
  system: 'http://terminology.hl7.org/CodeSystem/practitioner-role',
};
const practitioner_role_Nurse: Coding = {
  code: 'nurse',
  display: 'Nurse',
  system: 'http://terminology.hl7.org/CodeSystem/practitioner-role',
};
const practitioner_role_Pharmacist: Coding = {
  code: 'pharmacist',
  display: 'Pharmacist',
  system: 'http://terminology.hl7.org/CodeSystem/practitioner-role',
};
const practitioner_role_Researcher: Coding = {
  code: 'researcher',
  display: 'Researcher',
  system: 'http://terminology.hl7.org/CodeSystem/practitioner-role',
};
const practitioner_role_TeacherEducator: Coding = {
  code: 'teacher',
  display: 'Teacher/educator',
  system: 'http://terminology.hl7.org/CodeSystem/practitioner-role',
};
/**
 * This example value set defines a set of codes that can be used to indicate the role of a Practitioner.
 */
export const PractitionerRole = {
  /**
   * A qualified/registered medical practitioner
   */
  Doctor_practitioner_role: practitioner_role_Doctor,
  /**
   * Someone who is qualified in Information and Communication Technologies
   */
  ICTProfessional_practitioner_role: practitioner_role_ICTProfessional,
  /**
   * A practitioner with nursing experience that may be qualified/registered
   */
  Nurse_practitioner_role: practitioner_role_Nurse,
  /**
   * A qualified/registered/licensed pharmacist
   */
  Pharmacist_practitioner_role: practitioner_role_Pharmacist,
  /**
   * A practitioner that may perform research
   */
  Researcher_practitioner_role: practitioner_role_Researcher,
  /**
   * Someone who is able to provide educational services
   */
  TeacherEducator_practitioner_role: practitioner_role_TeacherEducator,
};
const practitioner_specialty_Cardiologist: Coding = {
  code: 'cardio',
  display: 'Cardiologist',
  system: 'http://hl7.org/fhir/practitioner-specialty',
};
const practitioner_specialty_Dentist: Coding = {
  code: 'dent',
  display: 'Dentist',
  system: 'http://hl7.org/fhir/practitioner-specialty',
};
const practitioner_specialty_DietaryConsultant: Coding = {
  code: 'dietary',
  display: 'Dietary consultant',
  system: 'http://hl7.org/fhir/practitioner-specialty',
};
const practitioner_specialty_Midwife: Coding = {
  code: 'midw',
  display: 'Midwife',
  system: 'http://hl7.org/fhir/practitioner-specialty',
};
const practitioner_specialty_SystemsArchitect: Coding = {
  code: 'sysarch',
  display: 'Systems architect',
  system: 'http://hl7.org/fhir/practitioner-specialty',
};
/**
 * This example value set defines a set of codes that can be used to indicate the specialty of a Practitioner.
 */
export const PractitionerSpecialty = {
  Cardiologist: practitioner_specialty_Cardiologist,
  Dentist: practitioner_specialty_Dentist,
  DietaryConsultant: practitioner_specialty_DietaryConsultant,
  Midwife: practitioner_specialty_Midwife,
  SystemsArchitect: practitioner_specialty_SystemsArchitect,
};
const precision_estimate_type_ConfidenceInterval: Coding = {
  code: 'CI',
  display: 'confidence interval',
  system: 'http://terminology.hl7.org/CodeSystem/precision-estimate-type',
};
const precision_estimate_type_InterquartileRange: Coding = {
  code: 'IQR',
  display: 'interquartile range',
  system: 'http://terminology.hl7.org/CodeSystem/precision-estimate-type',
};
const precision_estimate_type_StandardDeviation: Coding = {
  code: 'SD',
  display: 'standard deviation',
  system: 'http://terminology.hl7.org/CodeSystem/precision-estimate-type',
};
const precision_estimate_type_StandardError: Coding = {
  code: 'SE',
  display: 'standard error',
  system: 'http://terminology.hl7.org/CodeSystem/precision-estimate-type',
};
/**
 * Method of reporting variability of estimates, such as confidence intervals, interquartile range or standard deviation.
 */
export const PrecisionEstimateType = {
  /**
   * confidence interval.
   */
  ConfidenceInterval: precision_estimate_type_ConfidenceInterval,
  /**
   * interquartile range.
   */
  InterquartileRange: precision_estimate_type_InterquartileRange,
  /**
   * standard deviation.
   */
  StandardDeviation: precision_estimate_type_StandardDeviation,
  /**
   * standard error.
   */
  StandardError: precision_estimate_type_StandardError,
};
const v3_ProbabilityDistributionType_Beta: Coding = {
  code: 'B',
  display: 'beta',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProbabilityDistributionType',
};
const v3_ProbabilityDistributionType_Exponential: Coding = {
  code: 'E',
  display: 'exponential',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProbabilityDistributionType',
};
const v3_ProbabilityDistributionType_F: Coding = {
  code: 'F',
  display: 'F',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProbabilityDistributionType',
};
const v3_ProbabilityDistributionType_Gamma: Coding = {
  code: 'G',
  display: '(gamma)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProbabilityDistributionType',
};
const v3_ProbabilityDistributionType_LogNormal: Coding = {
  code: 'LN',
  display: 'log-normal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProbabilityDistributionType',
};
const v3_ProbabilityDistributionType_NormalGaussian: Coding = {
  code: 'N',
  display: 'normal (Gaussian)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProbabilityDistributionType',
};
const v3_ProbabilityDistributionType_T: Coding = {
  code: 'T',
  display: 'T',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProbabilityDistributionType',
};
const v3_ProbabilityDistributionType_Uniform: Coding = {
  code: 'U',
  display: 'uniform',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProbabilityDistributionType',
};
const v3_ProbabilityDistributionType_ChiSquare: Coding = {
  code: 'X2',
  display: 'chi square',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProbabilityDistributionType',
};
/**
 * Codes specifying the type of probability distribution.
 */
export const ProbabilityDistributionType = {
  /**
   * The beta-distribution is used for data that is bounded on both sides and may or may not be skewed (e.g., occurs when probabilities are estimated.)  Two parameters a and b  are available to adjust the curve.  The mean m and variance s2 relate as follows: m = a/ (a + b) and s2 = ab/((a + b)2 (a + b + 1)).
   */
  Beta: v3_ProbabilityDistributionType_Beta,
  /**
   * Used for data that describes extinction.  The exponential distribution is a special form of g-distribution where a = 1, hence, the relationship to mean m and variance s2 are m = b and s2 = b2.
   */
  Exponential: v3_ProbabilityDistributionType_Exponential,
  /**
   * Used to describe the quotient of two c2 random variables.  The F-distribution has two parameters n1 and n2, which are the numbers of degrees of freedom of the numerator and denominator variable respectively. The relationship to mean m  and variance s2 are: m = n2 / (n2 - 2) and s2 = (2 n2 (n2 + n1 - 2)) / (n1 (n2 - 2)2 (n2 - 4)).
   */
  F: v3_ProbabilityDistributionType_F,
  /**
   * The gamma-distribution used for data that is skewed and bounded to the right, i.e. where the maximum of the distribution curve is located near the origin.  The g-distribution has a two parameters a and b.  The relationship to mean m and variance s2 is m = a b and s2 = a b2.
   */
  Gamma: v3_ProbabilityDistributionType_Gamma,
  /**
   * The logarithmic normal distribution is used to transform skewed random variable X into a normally distributed random variable U = log X. The log-normal distribution can be specified with the properties mean m and standard deviation s.  Note however that mean m and standard deviation s are the parameters of the raw value distribution, not the transformed parameters of the lognormal distribution that are conventionally referred to by the same letters.  Those log-normal parameters mlog and slog relate to the mean m and standard deviation s of the data value through slog2 = log (s2/m2 + 1) and mlog = log m - slog2/2.
   */
  LogNormal: v3_ProbabilityDistributionType_LogNormal,
  /**
   * This is the well-known bell-shaped normal distribution.  Because of the central limit theorem, the normal distribution is the distribution of choice for an unbounded random variable that is an outcome of a combination of many stochastic processes.  Even for values bounded on a single side (i.e. greater than 0) the normal distribution may be accurate enough if the mean is "far away" from the bound of the scale measured in terms of standard deviations.
   */
  NormalGaussian: v3_ProbabilityDistributionType_NormalGaussian,
  /**
   * Used to describe the quotient of a normal random variable and the square root of a c2 random variable.  The t-distribution has one parameter n, the number of degrees of freedom. The relationship to mean m  and variance s2 are: m = 0 and s2 = n / (n - 2)
   */
  T: v3_ProbabilityDistributionType_T,
  /**
   * The uniform distribution assigns a constant probability over the entire interval of possible outcomes, while all outcomes outside this interval are assumed to have zero probability.  The width of this interval is 2s sqrt(3).  Thus, the uniform distribution assigns the probability densities f(x) = sqrt(2 s sqrt(3))  to values m - s sqrt(3) >= x <= m + s sqrt(3) and f(x) = 0 otherwise.
   */
  Uniform: v3_ProbabilityDistributionType_Uniform,
  /**
   * Used to describe the sum of squares of random variables which occurs when a variance is estimated (rather than presumed) from the sample.  The only parameter of the c2-distribution is n, so called the number of degrees of freedom (which is the number of independent parts in the sum).  The c2-distribution is a special type of g-distribution with parameter a = n /2 and b  = 2.  Hence, m = n and s2 = 2 n.
   */
  ChiSquare: v3_ProbabilityDistributionType_ChiSquare,
};
const ProcedureCategory_VAL103693007: Coding = {
  code: '103693007',
  system: 'http://snomed.info/sct',
};
const ProcedureCategory_VAL24642003: Coding = {
  code: '24642003',
  system: 'http://snomed.info/sct',
};
const ProcedureCategory_VAL387713003: Coding = {
  code: '387713003',
  system: 'http://snomed.info/sct',
};
const ProcedureCategory_VAL409063005: Coding = {
  code: '409063005',
  system: 'http://snomed.info/sct',
};
const ProcedureCategory_VAL409073007: Coding = {
  code: '409073007',
  system: 'http://snomed.info/sct',
};
const ProcedureCategory_VAL410606002: Coding = {
  code: '410606002',
  system: 'http://snomed.info/sct',
};
const ProcedureCategory_VAL46947000: Coding = {
  code: '46947000',
  system: 'http://snomed.info/sct',
};
/**
 * Procedure Category code: A selection of relevant SNOMED CT codes.
 */
export const ProcedureCategory = {
  VAL103693007: ProcedureCategory_VAL103693007,
  VAL24642003: ProcedureCategory_VAL24642003,
  VAL387713003: ProcedureCategory_VAL387713003,
  VAL409063005: ProcedureCategory_VAL409063005,
  VAL409073007: ProcedureCategory_VAL409073007,
  VAL410606002: ProcedureCategory_VAL410606002,
  VAL46947000: ProcedureCategory_VAL46947000,
};
const ProcedureFollowup_VAL18949003: Coding = {
  code: '18949003',
  system: 'http://snomed.info/sct',
};
const ProcedureFollowup_VAL225164002: Coding = {
  code: '225164002',
  system: 'http://snomed.info/sct',
};
const ProcedureFollowup_VAL229506003: Coding = {
  code: '229506003',
  system: 'http://snomed.info/sct',
};
const ProcedureFollowup_VAL241031001: Coding = {
  code: '241031001',
  system: 'http://snomed.info/sct',
};
const ProcedureFollowup_VAL274441001: Coding = {
  code: '274441001',
  system: 'http://snomed.info/sct',
};
const ProcedureFollowup_VAL30549001: Coding = {
  code: '30549001',
  system: 'http://snomed.info/sct',
};
const ProcedureFollowup_VAL35963001: Coding = {
  code: '35963001',
  system: 'http://snomed.info/sct',
};
const ProcedureFollowup_VAL359825008: Coding = {
  code: '359825008',
  system: 'http://snomed.info/sct',
};
const ProcedureFollowup_VAL394725008: Coding = {
  code: '394725008',
  system: 'http://snomed.info/sct',
};
const ProcedureFollowup_VAL447346005: Coding = {
  code: '447346005',
  system: 'http://snomed.info/sct',
};
/**
 * Procedure follow up codes: A selection of SNOMED CT codes relevant to procedure follow up.
 */
export const ProcedureFollowup = {
  VAL18949003: ProcedureFollowup_VAL18949003,
  VAL225164002: ProcedureFollowup_VAL225164002,
  VAL229506003: ProcedureFollowup_VAL229506003,
  VAL241031001: ProcedureFollowup_VAL241031001,
  VAL274441001: ProcedureFollowup_VAL274441001,
  VAL30549001: ProcedureFollowup_VAL30549001,
  VAL35963001: ProcedureFollowup_VAL35963001,
  VAL359825008: ProcedureFollowup_VAL359825008,
  VAL394725008: ProcedureFollowup_VAL394725008,
  VAL447346005: ProcedureFollowup_VAL447346005,
};
const ProcedureOutcome_VAL385669000: Coding = {
  code: '385669000',
  system: 'http://snomed.info/sct',
};
const ProcedureOutcome_VAL385670004: Coding = {
  code: '385670004',
  system: 'http://snomed.info/sct',
};
const ProcedureOutcome_VAL385671000: Coding = {
  code: '385671000',
  system: 'http://snomed.info/sct',
};
/**
 * Procedure Outcome code: A selection of relevant SNOMED CT codes.
 */
export const ProcedureOutcome = {
  VAL385669000: ProcedureOutcome_VAL385669000,
  VAL385670004: ProcedureOutcome_VAL385670004,
  VAL385671000: ProcedureOutcome_VAL385671000,
};
const procedure_progress_status_codes_AnesthesiaInduced: Coding = {
  code: 'anesthesia-induced',
  display: 'Anesthesia Induced',
  system: 'http://hl7.org/fhir/procedure-progress-status-code',
};
const procedure_progress_status_codes_ClosedIncision: Coding = {
  code: 'closed-incision',
  display: 'Closed Incision',
  system: 'http://hl7.org/fhir/procedure-progress-status-code',
};
const procedure_progress_status_codes_InOperatingRoom: Coding = {
  code: 'in-operating-room',
  display: 'In Operating Room',
  system: 'http://hl7.org/fhir/procedure-progress-status-code',
};
const procedure_progress_status_codes_InRecoveryRoom: Coding = {
  code: 'in-recovery-room',
  display: 'In Recovery Room',
  system: 'http://hl7.org/fhir/procedure-progress-status-code',
};
const procedure_progress_status_codes_OpenIncision: Coding = {
  code: 'open-incision',
  display: 'Open Incision',
  system: 'http://hl7.org/fhir/procedure-progress-status-code',
};
const procedure_progress_status_codes_Prepared: Coding = {
  code: 'prepared',
  display: 'Prepared',
  system: 'http://hl7.org/fhir/procedure-progress-status-code',
};
/**
 * This value set is provided as an example. The value set to instantiate this attribute should be drawn from a robust terminology code system that consists of or contains concepts to support the procedure performance process.
 */
export const ProcedureProgressStatusCodes = {
  /**
   * The patient is under anesthesia.
   */
  AnesthesiaInduced: procedure_progress_status_codes_AnesthesiaInduced,
  /**
   * The patient has incision(s) closed.
   */
  ClosedIncision: procedure_progress_status_codes_ClosedIncision,
  /**
   * A patient is in the Operating Room.
   */
  InOperatingRoom: procedure_progress_status_codes_InOperatingRoom,
  /**
   * The patient is in the recovery room.
   */
  InRecoveryRoom: procedure_progress_status_codes_InRecoveryRoom,
  /**
   * The patient has open incision(s).
   */
  OpenIncision: procedure_progress_status_codes_OpenIncision,
  /**
   * The patient is prepared for a procedure.
   */
  Prepared: procedure_progress_status_codes_Prepared,
};
const process_priority_Deferred: Coding = {
  code: 'deferred',
  display: 'Deferred',
  system: 'http://terminology.hl7.org/CodeSystem/processpriority',
};
const process_priority_Normal: Coding = {
  code: 'normal',
  display: 'Normal',
  system: 'http://terminology.hl7.org/CodeSystem/processpriority',
};
const process_priority_Immediate: Coding = {
  code: 'stat',
  display: 'Immediate',
  system: 'http://terminology.hl7.org/CodeSystem/processpriority',
};
/**
 * This value set includes the financial processing priority codes.
 */
export const ProcessPriority = {
  /**
   * Later, when possible.
   */
  Deferred: process_priority_Deferred,
  /**
   * With best effort.
   */
  Normal: process_priority_Normal,
  /**
   * Immediately in real time.
   */
  Immediate: process_priority_Immediate,
};
const product_category_BiologicalAgent: Coding = {
  code: 'biologicalAgent',
  display: 'BiologicalAgent',
  system: 'http://hl7.org/fhir/product-category',
};
const product_category_Cells: Coding = {
  code: 'cells',
  display: 'Cells',
  system: 'http://hl7.org/fhir/product-category',
};
const product_category_Fluid: Coding = {
  code: 'fluid',
  display: 'Fluid',
  system: 'http://hl7.org/fhir/product-category',
};
const product_category_Organ: Coding = {
  code: 'organ',
  display: 'Organ',
  system: 'http://hl7.org/fhir/product-category',
};
const product_category_Tissue: Coding = {
  code: 'tissue',
  display: 'Tissue',
  system: 'http://hl7.org/fhir/product-category',
};
/**
 * Biologically Derived Product Category.
 */
export const ProductCategory = {
  /**
   * Biological agent of unspecified type.
   */
  BiologicalAgent: product_category_BiologicalAgent,
  /**
   * Collection of cells.
   */
  Cells: product_category_Cells,
  /**
   * Body fluid.
   */
  Fluid: product_category_Fluid,
  /**
   * A collection of tissues joined in a structural unit to serve a common function.
   */
  Organ: product_category_Organ,
  /**
   * An ensemble of similar cells and their extracellular matrix from the same origin that together carry out a specific function.
   */
  Tissue: product_category_Tissue,
};
const product_status_Available: Coding = {
  code: 'available',
  display: 'Available',
  system: 'http://hl7.org/fhir/product-status',
};
const product_status_Unavailable: Coding = {
  code: 'unavailable',
  display: 'Unavailable',
  system: 'http://hl7.org/fhir/product-status',
};
/**
 * Biologically Derived Product Status.
 */
export const ProductStatus = {
  /**
   * Product is currently available for use.
   */
  Available: product_status_Available,
  /**
   * Product is not currently available for use.
   */
  Unavailable: product_status_Unavailable,
};
const product_storage_scale_Celsius: Coding = {
  code: 'celsius',
  display: 'Celsius',
  system: 'http://hl7.org/fhir/product-storage-scale',
};
const product_storage_scale_Fahrenheit: Coding = {
  code: 'farenheit',
  display: 'Fahrenheit',
  system: 'http://hl7.org/fhir/product-storage-scale',
};
const product_storage_scale_Kelvin: Coding = {
  code: 'kelvin',
  display: 'Kelvin',
  system: 'http://hl7.org/fhir/product-storage-scale',
};
/**
 * BiologicallyDerived Product Storage Scale.
 */
export const ProductStorageScale = {
  /**
   * Celsius or centigrade temperature scale.
   */
  Celsius: product_storage_scale_Celsius,
  /**
   * Fahrenheit temperature scale.
   */
  Fahrenheit: product_storage_scale_Fahrenheit,
  /**
   * Kelvin absolute thermodynamic temperature scale.
   */
  Kelvin: product_storage_scale_Kelvin,
};
const program_AcquiredBrainInjuryABIProgram: Coding = {
  code: '1',
  display: 'Acquired Brain Injury (ABI) Program ',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_CognitiveDementiaMemoryServiceCDAMS: Coding = {
  code: '10',
  display: 'Cognitive Dementia Memory Service (CDAMS)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_ChildFIRST: Coding = {
  code: '11',
  display: 'ChildFIRST',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_ChildrenQuoteSContactServices: Coding = {
  code: '12',
  display: "Children's Contact Services",
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_CommunityVisitorsScheme: Coding = {
  code: '13',
  display: 'Community Visitors Scheme',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_CPPCommunityPartnersProgram: Coding = {
  code: '14',
  display: 'CPP (Community Partners Program)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_ClosingTheGapCTG: Coding = {
  code: '15',
  display: 'Closing the Gap (CTG)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_CoordinatedVeteransQuoteCareCVCProgram: Coding = {
  code: '16',
  display: "Coordinated Veterans' Care (CVC) Program",
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_DayProgram: Coding = {
  code: '17',
  display: 'Day Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_DropInProgram: Coding = {
  code: '18',
  display: 'Drop In Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_EarlyYearsProgram: Coding = {
  code: '19',
  display: 'Early Years Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_ABISlowToRecoverABISTRProgram: Coding = {
  code: '2',
  display: 'ABI Slow To Recover (ABI STR) Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_EmployeeAssistanceProgram: Coding = {
  code: '20',
  display: 'Employee Assistance Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_HomeAndCommunityCareHACC: Coding = {
  code: '21',
  display: 'Home And Community Care (HACC)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_HospitalAdmissionRiskProgramHARP: Coding = {
  code: '22',
  display: 'Hospital Admission Risk Program (HARP)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_HospitalInTheHomeHITHProgram: Coding = {
  code: '23',
  display: 'Hospital in the Home (HITH) Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_ICTPIntensiveCommunityTreatmentProgram: Coding = {
  code: '24',
  display: 'ICTP (Intensive Community Treatment Program)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_IFSSIntensiveFamilySupportProgram: Coding = {
  code: '25',
  display: 'IFSS (Intensive Family Support Program)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_JPETJobPlacementEducationAndTraining: Coding = {
  code: '26',
  display: 'JPET (Job Placement, Education and Training)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_KooriJuvenileJusticeProgram: Coding = {
  code: '27',
  display: 'Koori Juvenile Justice Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_LanguageLiteracyAndNumeracyProgram: Coding = {
  code: '28',
  display: 'Language Literacy and Numeracy Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_LifeSkillsProgram: Coding = {
  code: '29',
  display: 'Life Skills Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_AccessPrograms: Coding = {
  code: '3',
  display: 'Access Programs',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_LMPLifestyleModificationProgram: Coding = {
  code: '30',
  display: 'LMP (Lifestyle Modification Program)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_MedsCheckProgram: Coding = {
  code: '31',
  display: 'MedsCheck Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_MethadoneBuprenorphineProgram: Coding = {
  code: '32',
  display: 'Methadone/Buprenorphine Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_NationalDisabilitiesInsuranceSchemeNDIS: Coding = {
  code: '33',
  display: 'National Disabilities Insurance Scheme (NDIS)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_NationalDiabetesServicesSchemeNDSS: Coding = {
  code: '34',
  display: 'National Diabetes Services Scheme (NDSS)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_NeedleSyringeProgram: Coding = {
  code: '35',
  display: 'Needle/Syringe Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_NPEPProgram: Coding = {
  code: '36',
  display: 'nPEP Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_PersonalSupportProgram: Coding = {
  code: '37',
  display: 'Personal Support Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_PartnersInRecoveryPIRProgram: Coding = {
  code: '38',
  display: 'Partners in Recovery (PIR) Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_PreEmploymentProgram: Coding = {
  code: '39',
  display: 'Pre-employment Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_AdultAndFurtherEducationACFEProgram: Coding = {
  code: '4',
  display: 'Adult and Further Education (ACFE) Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_ReconnectProgram: Coding = {
  code: '40',
  display: 'Reconnect Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_SexualAbuseCounsellingAndPreventionProgramSACPP: Coding = {
  code: '41',
  display: 'Sexual Abuse Counselling and Prevention Program (SACPP)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_SocialSupportPrograms: Coding = {
  code: '42',
  display: 'Social Support Programs',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_SupportedResidentialServiceSRS: Coding = {
  code: '43',
  display: 'Supported Residential Service (SRS)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_TasmanianAboriginalCentreTAC: Coding = {
  code: '44',
  display: 'Tasmanian Aboriginal Centre (TAC)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_VictimQuoteSAssistanceProgram: Coding = {
  code: '45',
  display: "Victim's Assistance Program",
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_AdultDayActivityAndSupportServicesADASSProgram: Coding = {
  code: '5',
  display: 'Adult Day Activity and Support Services (ADASS) Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_AdultDayCareProgram: Coding = {
  code: '6',
  display: 'Adult Day Care Program',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_ATSSAdultTrainingSupportService: Coding = {
  code: '7',
  display: 'ATSS (Adult Training Support Service)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_CommunityAgedCarePackagesCACP: Coding = {
  code: '8',
  display: 'Community Aged Care Packages (CACP)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
const program_CareCoordinationAndSupplementaryServicesCCSS: Coding = {
  code: '9',
  display: 'Care Coordination & Supplementary Services (CCSS)',
  system: 'http://terminology.hl7.org/CodeSystem/program',
};
/**
 * This value set defines an example set of codes that could be can be used to classify groupings of service-types/specialties.
 */
export const Program = {
  AcquiredBrainInjuryABIProgram: program_AcquiredBrainInjuryABIProgram,
  CognitiveDementiaMemoryServiceCDAMS: program_CognitiveDementiaMemoryServiceCDAMS,
  ChildFIRST: program_ChildFIRST,
  ChildrenQuoteSContactServices: program_ChildrenQuoteSContactServices,
  CommunityVisitorsScheme: program_CommunityVisitorsScheme,
  CPPCommunityPartnersProgram: program_CPPCommunityPartnersProgram,
  ClosingTheGapCTG: program_ClosingTheGapCTG,
  CoordinatedVeteransQuoteCareCVCProgram: program_CoordinatedVeteransQuoteCareCVCProgram,
  DayProgram: program_DayProgram,
  DropInProgram: program_DropInProgram,
  EarlyYearsProgram: program_EarlyYearsProgram,
  ABISlowToRecoverABISTRProgram: program_ABISlowToRecoverABISTRProgram,
  EmployeeAssistanceProgram: program_EmployeeAssistanceProgram,
  HomeAndCommunityCareHACC: program_HomeAndCommunityCareHACC,
  HospitalAdmissionRiskProgramHARP: program_HospitalAdmissionRiskProgramHARP,
  HospitalInTheHomeHITHProgram: program_HospitalInTheHomeHITHProgram,
  ICTPIntensiveCommunityTreatmentProgram: program_ICTPIntensiveCommunityTreatmentProgram,
  IFSSIntensiveFamilySupportProgram: program_IFSSIntensiveFamilySupportProgram,
  JPETJobPlacementEducationAndTraining: program_JPETJobPlacementEducationAndTraining,
  KooriJuvenileJusticeProgram: program_KooriJuvenileJusticeProgram,
  LanguageLiteracyAndNumeracyProgram: program_LanguageLiteracyAndNumeracyProgram,
  LifeSkillsProgram: program_LifeSkillsProgram,
  AccessPrograms: program_AccessPrograms,
  LMPLifestyleModificationProgram: program_LMPLifestyleModificationProgram,
  MedsCheckProgram: program_MedsCheckProgram,
  MethadoneBuprenorphineProgram: program_MethadoneBuprenorphineProgram,
  NationalDisabilitiesInsuranceSchemeNDIS: program_NationalDisabilitiesInsuranceSchemeNDIS,
  NationalDiabetesServicesSchemeNDSS: program_NationalDiabetesServicesSchemeNDSS,
  NeedleSyringeProgram: program_NeedleSyringeProgram,
  NPEPProgram: program_NPEPProgram,
  PersonalSupportProgram: program_PersonalSupportProgram,
  PartnersInRecoveryPIRProgram: program_PartnersInRecoveryPIRProgram,
  PreEmploymentProgram: program_PreEmploymentProgram,
  AdultAndFurtherEducationACFEProgram: program_AdultAndFurtherEducationACFEProgram,
  ReconnectProgram: program_ReconnectProgram,
  SexualAbuseCounsellingAndPreventionProgramSACPP: program_SexualAbuseCounsellingAndPreventionProgramSACPP,
  SocialSupportPrograms: program_SocialSupportPrograms,
  SupportedResidentialServiceSRS: program_SupportedResidentialServiceSRS,
  TasmanianAboriginalCentreTAC: program_TasmanianAboriginalCentreTAC,
  VictimQuoteSAssistanceProgram: program_VictimQuoteSAssistanceProgram,
  AdultDayActivityAndSupportServicesADASSProgram: program_AdultDayActivityAndSupportServicesADASSProgram,
  AdultDayCareProgram: program_AdultDayCareProgram,
  ATSSAdultTrainingSupportService: program_ATSSAdultTrainingSupportService,
  CommunityAgedCarePackagesCACP: program_CommunityAgedCarePackagesCACP,
  CareCoordinationAndSupplementaryServicesCCSS: program_CareCoordinationAndSupplementaryServicesCCSS,
};
const property_representation_CDATextFormat: Coding = {
  code: 'cdaText',
  display: 'CDA Text Format',
  system: 'http://hl7.org/fhir/property-representation',
};
const property_representation_TypeAttribute: Coding = {
  code: 'typeAttr',
  display: 'Type Attribute',
  system: 'http://hl7.org/fhir/property-representation',
};
const property_representation_XHTML: Coding = {
  code: 'xhtml',
  display: 'XHTML',
  system: 'http://hl7.org/fhir/property-representation',
};
const property_representation_XMLAttribute: Coding = {
  code: 'xmlAttr',
  display: 'XML Attribute',
  system: 'http://hl7.org/fhir/property-representation',
};
const property_representation_XMLText: Coding = {
  code: 'xmlText',
  display: 'XML Text',
  system: 'http://hl7.org/fhir/property-representation',
};
/**
 * How a property is represented when serialized.
 */
export const PropertyRepresentation = {
  /**
   * Use CDA narrative instead of XHTML.
   */
  CDATextFormat: property_representation_CDATextFormat,
  /**
   * The type of this element is indicated using xsi:type.
   */
  TypeAttribute: property_representation_TypeAttribute,
  /**
   * The property is represented using XHTML.
   */
  XHTML: property_representation_XHTML,
  /**
   * In XML, this property is represented as an attribute not an element.
   */
  XMLAttribute: property_representation_XMLAttribute,
  /**
   * This element is represented using the XML text attribute (primitives only).
   */
  XMLText: property_representation_XMLText,
};
const v3_ParticipationType_ParticipationAncillary: Coding = {
  code: '_ParticipationAncillary',
  display: 'ParticipationAncillary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_ParticipationInformationGenerator: Coding = {
  code: '_ParticipationInformationGenerator',
  display: 'ParticipationInformationGenerator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Analyte: Coding = {
  code: 'ALY',
  display: 'analyte',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ActCode_Anonymize: Coding = {
  code: 'ANONY',
  display: 'anonymize',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_DataOperation_Append: Coding = {
  code: 'APPEND',
  display: 'append',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_ParticipationType_Baby: Coding = {
  code: 'BBY',
  display: 'baby',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Beneficiary: Coding = {
  code: 'BEN',
  display: 'beneficiary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_CausativeAgent: Coding = {
  code: 'CAGNT',
  display: 'causative agent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Catalyst: Coding = {
  code: 'CAT',
  display: 'catalyst',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_CoverageTarget: Coding = {
  code: 'COV',
  display: 'coverage target',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_DataOperation_Create: Coding = {
  code: 'CREATE',
  display: 'create',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_ParticipationType_Consumable: Coding = {
  code: 'CSM',
  display: 'consumable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ActCode_Deidentify: Coding = {
  code: 'DEID',
  display: 'deidentify',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_DataOperation_Delete: Coding = {
  code: 'DELETE',
  display: 'delete',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_ParticipationType_Device: Coding = {
  code: 'DEV',
  display: 'device',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_DirectTarget: Coding = {
  code: 'DIR',
  display: 'direct target',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Distributor: Coding = {
  code: 'DIST',
  display: 'distributor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Destination: Coding = {
  code: 'DST',
  display: 'destination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_EntryLocation: Coding = {
  code: 'ELOC',
  display: 'entry location',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_ExposureAgent: Coding = {
  code: 'EXPAGNT',
  display: 'ExposureAgent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_ExposureParticipation: Coding = {
  code: 'EXPART',
  display: 'ExposureParticipation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_ExposureTarget: Coding = {
  code: 'EXPTRGT',
  display: 'ExposureTarget',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_ExposureSource: Coding = {
  code: 'EXSRC',
  display: 'ExposureSource',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_GuarantorParty: Coding = {
  code: 'GUAR',
  display: 'guarantor party',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Holder: Coding = {
  code: 'HLD',
  display: 'holder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_IndirectTarget: Coding = {
  code: 'IND',
  display: 'indirect target',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_DocumentCompletion_LegallyAuthenticated: Coding = {
  code: 'LA',
  display: 'legally authenticated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DocumentCompletion',
};
const v3_ActCode_AssignSecurityLabel: Coding = {
  code: 'LABEL',
  display: 'assign security label',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ParticipationType_Location: Coding = {
  code: 'LOC',
  display: 'location',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ActCode_Mask: Coding = {
  code: 'MASK',
  display: 'mask',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ParticipationType_UgentNotificationContact: Coding = {
  code: 'NOT',
  display: 'ugent notification contact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_NonReuseableDevice: Coding = {
  code: 'NRD',
  display: 'non-reuseable device',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_DataOperation_Nullify: Coding = {
  code: 'NULLIFY',
  display: 'nullify',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_ParticipationType_Origin: Coding = {
  code: 'ORG',
  display: 'origin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_PrimaryInformationRecipient: Coding = {
  code: 'PRCP',
  display: 'primary information recipient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Product: Coding = {
  code: 'PRD',
  display: 'product',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Performer: Coding = {
  code: 'PRF',
  display: 'performer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ActCode_Pseudonymize: Coding = {
  code: 'PSEUD',
  display: 'pseudonymize',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ParticipationType_RecordTarget: Coding = {
  code: 'RCT',
  display: 'record target',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Receiver: Coding = {
  code: 'RCV',
  display: 'receiver',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_ReusableDevice: Coding = {
  code: 'RDV',
  display: 'reusable device',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_ReferredBy: Coding = {
  code: 'REFB',
  display: 'Referred By',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_ReferredTo: Coding = {
  code: 'REFT',
  display: 'Referred to',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Remote: Coding = {
  code: 'RML',
  display: 'remote',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Subject: Coding = {
  code: 'SBJ',
  display: 'subject',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_Specimen: Coding = {
  code: 'SPC',
  display: 'specimen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_ParticipationType_TherapeuticAgent: Coding = {
  code: 'TPA',
  display: 'therapeutic agent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
const v3_DataOperation_Revise: Coding = {
  code: 'UPDATE',
  display: 'revise',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_ParticipationType_Via: Coding = {
  code: 'VIA',
  display: 'via',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
};
/**
 * This value set contains representative Activity Type codes, which includes codes from the HL7 DocumentCompletion, ActStatus, and DataOperations code system, W3C PROV-DM and PROV-N concepts and display names, several HL7 Lifecycle Event codes for which there are agreed upon definitions, and non-duplicated codes from the HL7 Security and Privacy Ontology Operations codes.
 */
export const ProvenanceActivityType = {
  /**
   * Participations related, but not primary to an act. The Referring, Admitting, and Discharging practitioners must be the same person as those authoring the ControlAct event for their respective trigger events.
   */
  ParticipationAncillary_v3_ParticipationType: v3_ParticipationType_ParticipationAncillary,
  /**
   * Parties that may or should contribute or have contributed information to the Act. Such information includes information leading to the decision to perform the Act and how to perform the Act (e.g., consultant), information that the Act itself seeks to reveal (e.g., informant of clinical history), or information about what Act was performed (e.g., informant witness).
   */
  ParticipationInformationGenerator_v3_ParticipationType: v3_ParticipationType_ParticipationInformationGenerator,
  /**
   * The practitioner who is responsible for admitting a patient to a patient encounter.
   */
  Admitter_v3_ParticipationType: v3_ParticipationType_Admitter,
  /**
   * The target of an Observation action. Links an observation to a Role whose player is the substance or most specific component entity (material, micro-organism, etc.) being measured within the subject.
   *
   *
   *                            Examples: A "plasma porcelain substance concentration" has analyte a Role with player substance Entity "porcelain".
   *
   *
   *                            UsageNotes: The Role that this participation connects to may be any Role whose player is that substance measured. Very often, the scoper may indicate the system in which the component is being measured. E.g., for "plasma porcelain" the scoper could be "Plasma".
   */
  Analyte_v3_ParticipationType: v3_ParticipationType_Analyte,
  /**
   * Custodian system must remove any information that could result in identifying the information subject.
   */
  Anonymize_v3_ActCode: v3_ActCode_Anonymize,
  /**
   * Description:Fundamental operation in an Information System (IS) that results only in the addition of information to an object already in existence. Note: The preceding definition is taken from the HL7 RBAC specification.  There is no restriction on how the operation is invoked, e.g., via a user interface.
   */
  Append_v3_DataOperation: v3_DataOperation_Append,
  /**
   * The practitioner that has responsibility for overseeing a patient's care during a patient encounter.
   */
  Attender_v3_ParticipationType: v3_ParticipationType_Attender,
  /**
   * Definition: A party that originates the Act and therefore has responsibility for the information given in the Act and ownership of this Act.
   *
   *
   *                            Example: the report writer, the person writing the act definition, the guideline author, the placer of an order, the EKG cart (device) creating a report etc. Every Act should have an author. Authorship is regardless of mood always actual authorship.
   *
   *                         Examples of such policies might include:
   *
   *
   *
   *                               The author and anyone they explicitly delegate may update the report;
   *
   *
   *
   *                               All administrators within the same clinic may cancel and reschedule appointments created by other administrators within that clinic;
   *
   *
   *
   *                         A party that is neither an author nor a party who is extended authorship maintenance rights by policy, may only amend, reverse, override, replace, or follow up in other ways on this Act, whereby the Act remains intact and is linked to another Act authored by that other party.
   */
  AuthorOriginator_v3_ParticipationType: v3_ParticipationType_AuthorOriginator,
  /**
   * A verifier who attests to the accuracy of an act, but who does not have privileges to legally authenticate the act. An example would be a resident physician who sees a patient and dictates a note, then later signs it. Their signature constitutes an authentication.
   */
  Authenticator_v3_ParticipationType: v3_ParticipationType_Authenticator,
  /**
   * In an obstetric service, the baby.
   */
  Baby_v3_ParticipationType: v3_ParticipationType_Baby,
  /**
   * Target on behalf of whom the service happens, but that is not necessarily present in the service.  Can occur together with direct target to indicate that a target is both, as in the case where the patient is the indirect beneficiary of a service rendered to a family member, e.g. counseling or given home care instructions.  This concept includes a participant, such as a covered party, who derives benefits from a service act covered by a coverage act.
   *
   *                         Note that the semantic role of the intended recipient who benefits from the happening denoted by the verb in the clause.  Thus, a patient who has no coverage under a policy or program may be a beneficiary of a health service while not being the beneficiary of coverage for that service.
   */
  Beneficiary_v3_ParticipationType: v3_ParticipationType_Beneficiary,
  /**
   * Definition: A factor, such as a microorganism, chemical substance, or form of radiation, whose presence, excessive presence, or (in deficiency diseases) relative absence is essential, in whole or in part, for the occurrence of a condition.
   *
   *                         Constraint:  The use of this participation is limited to observations.
   */
  CausativeAgent_v3_ParticipationType: v3_ParticipationType_CausativeAgent,
  /**
   * A person or organization who should be contacted for follow-up questions about the act in place of the author.
   */
  CallbackContact_v3_ParticipationType: v3_ParticipationType_CallbackContact,
  /**
   * The catalyst of a chemical reaction, such as an enzyme or a platinum surface. In biochemical reactions, connects the enzyme with the molecular interaction
   */
  Catalyst_v3_ParticipationType: v3_ParticipationType_Catalyst,
  /**
   * An advisor participating in the service by performing evaluations and making recommendations.
   */
  Consultant_v3_ParticipationType: v3_ParticipationType_Consultant,
  /**
   * The target participation for an individual in a health care coverage act in which the target role is either the policy holder of the coverage, or a covered party under the coverage.
   */
  CoverageTarget_v3_ParticipationType: v3_ParticipationType_CoverageTarget,
  /**
   * Description:Fundamental operation in an Information System (IS) that results only in the act of bringing an object into existence. Note: The preceding definition is taken from the HL7 RBAC specification.  There is no restriction on how the operation is invoked, e.g., via a user interface. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Create_v3_DataOperation: v3_DataOperation_Create,
  /**
   * Participant material that is taken up, diminished, altered, or disappears in the act.
   */
  Consumable_v3_ParticipationType: v3_ParticipationType_Consumable,
  /**
   * An entity (person, organization or device) that is in charge of maintaining the information of this act (e.g., who maintains the report or the master service catalog item, etc.).
   */
  Custodian_v3_ParticipationType: v3_ParticipationType_Custodian,
  /**
   * Custodian system must strip information of data that would allow the identification of the source of the information or the information subject.
   */
  Deidentify_v3_ActCode: v3_ActCode_Deidentify,
  /**
   * Description:Fundamental operation in an Information System (IS) that results only in the removal of information about an object from memory or storage. Note: The preceding definition is taken from the HL7 RBAC specification.  There is no restriction on how the operation is invoked, e.g., via a user interface.
   */
  Delete_v3_DataOperation: v3_DataOperation_Delete,
  /**
   * Participant used in performing the act without being substantially affected by the act (i.e. durable or inert with respect to that particular service).
   *
   *
   *                            Examples: monitoring equipment, tools, but also access/drainage lines, prostheses, pace maker, etc.
   */
  Device_v3_ParticipationType: v3_ParticipationType_Device,
  /**
   * Target participant  that is substantially present in the act  and which is directly involved in the action (includes consumed material, devices, etc.).
   */
  DirectTarget_v3_ParticipationType: v3_ParticipationType_DirectTarget,
  /**
   * The practitioner who is responsible for the discharge of a patient from a patient encounter.
   */
  Discharger_v3_ParticipationType: v3_ParticipationType_Discharger,
  /**
   * Distributes material used in or generated during the act.
   */
  Distributor_v3_ParticipationType: v3_ParticipationType_Distributor,
  /**
   * In some organ transplantation services and rarely in transfusion services a donor will be a target participant in the service.  However, in most cases transplantation is decomposed in three services: explantation, transport, and implantation.  The identity of the donor (recipient) is often irrelevant for the explantation (implantation) service.
   */
  Donor_v3_ParticipationType: v3_ParticipationType_Donor,
  /**
   * The destination for services.  May be a static building (or room therein) or a movable facility (e.g., ship).
   */
  Destination_v3_ParticipationType: v3_ParticipationType_Destination,
  /**
   * A location where data about an Act was entered.
   */
  EntryLocation_v3_ParticipationType: v3_ParticipationType_EntryLocation,
  /**
   * A person entering the data into the originating system.  The data entry person is collected optionally for internal quality control purposes.  This includes the transcriptionist for dictated text.
   */
  DataEntryPerson_v3_ParticipationType: v3_ParticipationType_DataEntryPerson,
  /**
   * Only with Transportation services.  A person who escorts the patient.
   */
  Escort_v3_ParticipationType: v3_ParticipationType_Escort,
  /**
   * Description: The entity playing the associated role is the physical (including energy), chemical or biological substance that is participating in the exposure.  For example in communicable diseases, the associated playing entity is the disease causing pathogen.
   */
  ExposureAgent_v3_ParticipationType: v3_ParticipationType_ExposureAgent,
  /**
   * Description:Direct participation in an exposure act where it is unknown that the participant is the source or subject of the exposure.  If the participant is known to be the contact of an exposure then the SBJ participation type should be used.  If the participant is known to be the source then the EXSRC participation type should be used.
   */
  ExposureParticipation_v3_ParticipationType: v3_ParticipationType_ExposureParticipation,
  /**
   * Description: The entity playing the associated role is the target (contact) of exposure.
   */
  ExposureTarget_v3_ParticipationType: v3_ParticipationType_ExposureTarget,
  /**
   * Description:The entity playing the associated role is the source of exposure.
   */
  ExposureSource_v3_ParticipationType: v3_ParticipationType_ExposureSource,
  /**
   * The target person or organization contractually recognized by the issuer as a participant who has assumed fiscal responsibility for another personaTMs financial obligations by guaranteeing to pay for amounts owed to a particular account
   *
   *
   *                            Example:The subscriber of the patientaTMs health insurance policy signs a contract with the provider to be fiscally responsible for the patient billing account balance amount owed.
   */
  GuarantorParty_v3_ParticipationType: v3_ParticipationType_GuarantorParty,
  /**
   * Participant who posses an instrument such as a financial contract (insurance policy) usually based on some agreement with the author.
   */
  Holder_v3_ParticipationType: v3_ParticipationType_Holder,
  /**
   * Target that is not substantially present in the act and which is not directly affected by the act, but which will be a focus of the record or documentation of the act.
   */
  IndirectTarget_v3_ParticipationType: v3_ParticipationType_IndirectTarget,
  /**
   * A source of reported information (e.g., a next of kin who answers questions about the patient's history).  For history questions, the patient is logically an informant, yet the informant of history questions is implicitly the subject.
   */
  Informant_v3_ParticipationType: v3_ParticipationType_Informant,
  /**
   * A party, who may or should receive or who has recieved the Act or subsequent or derivative information of that Act. Information recipient is inert, i.e., independent of mood." Rationale: this is a generalization of a too diverse family that the definition can't be any more specific, and the concept is abstract so one of the specializations should be used.
   */
  InformationRecipient_v3_ParticipationType: v3_ParticipationType_InformationRecipient,
  /**
   * A completion status in which a document has been signed manually or electronically by the individual who is legally responsible for that document. This is the most mature state in the workflow progression.
   */
  LegallyAuthenticated_v3_DocumentCompletion: v3_DocumentCompletion_LegallyAuthenticated,
  /**
   * A verifier who legally authenticates the accuracy of an act. An example would be a staff physician who sees a patient and dictates a note, then later signs it. Their signature constitutes a legal authentication.
   */
  LegalAuthenticator_v3_ParticipationType: v3_ParticipationType_LegalAuthenticator,
  /**
   * Custodian security system must assign and bind security labels in order to classify information created in the information systems under its control for collection, access, use and disclosure in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the assignment and binding.
   *
   *
   *                            Usage Note: In security systems, security policy label assignments do not change, they may supersede prior assignments, and such reassignments are always tracked for auditing and other purposes.
   */
  AssignSecurityLabel_v3_ActCode: v3_ActCode_AssignSecurityLabel,
  /**
   * The facility where the service is done.  May be a static building (or room therein) or a moving location (e.g., ambulance, helicopter, aircraft, train, truck, ship, etc.)
   */
  Location_v3_ParticipationType: v3_ParticipationType_Location,
  /**
   * Custodian system must render information unreadable and unusable by algorithmically transforming plaintext into ciphertext.  User may be provided a key to decrypt per license or "shared secret".
   */
  Mask_v3_ActCode: v3_ActCode_Mask,
  /**
   * An information recipient to notify for urgent matters about this Act. (e.g., in a laboratory order, critical results are being called by phone right away, this is the contact to call; or for an inpatient encounter, a next of kin to notify when the patient becomes critically ill).
   */
  UgentNotificationContact_v3_ParticipationType: v3_ParticipationType_UgentNotificationContact,
  /**
   * A device that changes ownership due to the service, e.g., a pacemaker, a prosthesis, an insulin injection equipment (pen), etc.  Such material may need to be restocked after he service.
   */
  NonReuseableDevice_v3_ParticipationType: v3_ParticipationType_NonReuseableDevice,
  /**
   * Description:Change the status of an object representing an Act to "nullified", i.e., treat as though it never existed. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Nullify_v3_DataOperation: v3_DataOperation_Nullify,
  /**
   * The location of origin for services.  May be a static building (or room therein) or a movable facility (e.g., ship).
   */
  Origin_v3_ParticipationType: v3_ParticipationType_Origin,
  /**
   * Indicates that the target of the participation is involved in some manner in the act, but does not qualify how.
   */
  Participation_v3_ParticipationType: v3_ParticipationType_Participation,
  /**
   * The principal or primary performer of the act.
   */
  PrimaryPerformer_v3_ParticipationType: v3_ParticipationType_PrimaryPerformer,
  /**
   * Information recipient to whom an act statement is primarily directed. E.g., a primary care provider receiving a discharge letter from a hospitalist, a health department receiving information on a suspected case of infectious disease. Multiple of these participations may exist on the same act without requiring that recipients be ranked as primary vs. secondary.
   */
  PrimaryInformationRecipient_v3_ParticipationType: v3_ParticipationType_PrimaryInformationRecipient,
  /**
   * Participant material that is brought forth (produced) in the act (e.g., specimen in a specimen collection, access or drainage in a placement service, medication package in a dispense service). It does not matter whether the material produced had existence prior to the service, or whether it is created in the service (e.g., in supply services the product is taken from a stock).
   */
  Product_v3_ParticipationType: v3_ParticipationType_Product,
  /**
   * Definition: A person, non-person living subject, organization or device that who actually and principally carries out the action. Device should only be assigned as a performer in circumstances where the device is performing independent of human intervention.  Need not be the principal responsible actor.
   *
   *
   *                            Exampe: A surgery resident operating under supervision of attending surgeon, a search and rescue dog locating survivors, an electronic laboratory analyzer or the laboratory discipline requested to perform a laboratory test. The performer may also be the patient in self-care, e.g. fingerstick blood sugar. The traditional order filler is a performer. This information should accompany every service event.
   *
   *
   *                            Note: that existing HL7 designs assign an organization as the playing entity of the Role that is the performer.  These designs should be revised in subsequent releases to make this the scooping entity for the role involved.
   */
  Performer_v3_ParticipationType: v3_ParticipationType_Performer,
  /**
   * Custodian system must strip information of data that would allow the identification of the source of the information or the information subject.  Custodian may retain a key to relink data necessary to reidentify the information subject.
   */
  Pseudonymize_v3_ActCode: v3_ActCode_Pseudonymize,
  /**
   * The record target indicates whose medical record holds the documentation of this act.  This is especially important when the subject of a service is not the patient himself.
   */
  RecordTarget_v3_ParticipationType: v3_ParticipationType_RecordTarget,
  /**
   * The person (or organization) who receives the product of an Act.
   */
  Receiver_v3_ParticipationType: v3_ParticipationType_Receiver,
  /**
   * A device that does not change ownership due to the service, i.e., a surgical instrument or tool or an endoscope.  The distinction between reuseable and non-reuseable must be made in order to know whether material must be re-stocked.
   */
  ReusableDevice_v3_ParticipationType: v3_ParticipationType_ReusableDevice,
  /**
   * A person having referred the subject of the service to the performer (referring physician).  Typically, a referring physician will receive a report.
   */
  Referrer_v3_ParticipationType: v3_ParticipationType_Referrer,
  /**
   * A participant (e.g. provider) who has referred the subject of an act (e.g. patient).
   *
   *                         Typically, a referred by participant will provide a report (e.g. referral).
   */
  ReferredBy_v3_ParticipationType: v3_ParticipationType_ReferredBy,
  /**
   * The person who receives the patient
   */
  ReferredTo_v3_ParticipationType: v3_ParticipationType_ReferredTo,
  /**
   * The person or organization that has primary responsibility for the act.  The responsible party is not necessarily present in an action, but is accountable for the action through the power to delegate, and the duty to review actions with the performing actor after the fact.  This responsibility may be ethical, legal, contractual, fiscal, or fiduciary in nature.
   *
   *
   *                            Example: A person who is the head of a biochemical laboratory; a sponsor for a policy or government program.
   */
  ResponsibleParty_v3_ParticipationType: v3_ParticipationType_ResponsibleParty,
  /**
   * Some services take place at multiple concurrent locations (e.g., telemedicine, telephone consultation).  The location where the principal performing actor is located is taken as the primary location (LOC) while the other location(s) are considered "remote."
   */
  Remote_v3_ParticipationType: v3_ParticipationType_Remote,
  /**
   * The principle target on which the action happens.
   *
   *
   *                            Examples: The patient in physical examination, a specimen in a lab observation. May also be a patient's family member (teaching) or a device or room (cleaning, disinfecting, housekeeping).
   *
   *
   *                            UsageNotes: Not all direct targets are subjects. Consumables and devices used as tools for an act are not subjects. However, a device may be a subject of a maintenance action.
   */
  Subject_v3_ParticipationType: v3_ParticipationType_Subject,
  /**
   * The subject of non-clinical (e.g. laboratory) observation services is a specimen.
   */
  Specimen_v3_ParticipationType: v3_ParticipationType_Specimen,
  /**
   * A person assisting in an act through his substantial presence and involvement   This includes: assistants, technicians, associates, or whatever the job titles may be.
   */
  SecondaryPerformer_v3_ParticipationType: v3_ParticipationType_SecondaryPerformer,
  /**
   * Something incorporated in the subject of a therapy service to achieve a physiologic effect (e.g., heal, relieve, provoke a condition, etc.) on the subject.  In an administration service the therapeutic agent is a consumable, in a preparation or dispense service, it is a product.  Thus, consumable or product must be specified in accordance with the kind of service.
   */
  TherapeuticAgent_v3_ParticipationType: v3_ParticipationType_TherapeuticAgent,
  /**
   * An entity entering the data into the originating system. The data entry entity is collected optionally for internal quality control purposes. This includes the transcriptionist for dictated text transcribed into electronic form.
   */
  Transcriber_v3_ParticipationType: v3_ParticipationType_Transcriber,
  /**
   * A secondary information recipient, who receives copies (e.g., a primary care provider receiving copies of results as ordered by specialist).
   */
  Tracker_v3_ParticipationType: v3_ParticipationType_Tracker,
  /**
   * Definition:Fundamental operation in an Information System (IS) that results only in the revision or alteration of an object. Note: The preceding definition is taken from the HL7 RBAC specification. There is no restriction on how the operation is invoked, e.g., via a user interface.
   */
  Revise_v3_DataOperation: v3_DataOperation_Revise,
  /**
   * For services, an intermediate location that specifies a path between origin an destination.
   */
  Via_v3_ParticipationType: v3_ParticipationType_Via,
  /**
   * A person who verifies the correctness and appropriateness of the service (plan, order, event, etc.) and hence takes on accountability.
   */
  Verifier_v3_ParticipationType: v3_ParticipationType_Verifier,
  /**
   * Only with service events.  A person witnessing the action happening without doing anything.  A witness is not necessarily aware, much less approves of anything stated in the service event.  Example for a witness is students watching an operation or an advanced directive witness.
   */
  Witness_v3_ParticipationType: v3_ParticipationType_Witness,
};
const provenance_agent_role_Assembler: Coding = {
  code: 'assembler',
  display: 'Assembler',
  system: 'http://hl7.org/fhir/provenance-participant-role',
};
const provenance_agent_role_Attester: Coding = {
  code: 'attester',
  display: 'Attester',
  system: 'http://hl7.org/fhir/provenance-participant-role',
};
const provenance_agent_role_Author: Coding = {
  code: 'author',
  display: 'Author',
  system: 'http://hl7.org/fhir/provenance-participant-role',
};
const provenance_agent_role_Composer: Coding = {
  code: 'composer',
  display: 'Composer',
  system: 'http://hl7.org/fhir/provenance-participant-role',
};
const provenance_agent_role_Custodian: Coding = {
  code: 'custodian',
  display: 'Custodian',
  system: 'http://hl7.org/fhir/provenance-participant-role',
};
const provenance_agent_role_Enterer: Coding = {
  code: 'enterer',
  display: 'Enterer',
  system: 'http://hl7.org/fhir/provenance-participant-role',
};
const provenance_agent_role_Informant: Coding = {
  code: 'informant',
  display: 'Informant',
  system: 'http://hl7.org/fhir/provenance-participant-role',
};
const provenance_agent_role_LegalAuthenticator: Coding = {
  code: 'legal',
  display: 'Legal Authenticator',
  system: 'http://hl7.org/fhir/provenance-participant-role',
};
const provenance_agent_role_Performer: Coding = {
  code: 'performer',
  display: 'Performer',
  system: 'http://hl7.org/fhir/provenance-participant-role',
};
const provenance_agent_role_Verifier: Coding = {
  code: 'verifier',
  display: 'Verifier',
  system: 'http://hl7.org/fhir/provenance-participant-role',
};
/**
 * The role that a provenance participant played
 */
export const ProvenanceAgentRole = {
  /**
   * A device that operates independently of an author on custodian's algorithms for data extraction of existing information for purpose of generating a new artifact.
   */
  Assembler: provenance_agent_role_Assembler,
  /**
   * A verifier who attests to the accuracy of the resource
   */
  Attester: provenance_agent_role_Attester,
  /**
   * A party that originates the resource and therefore has responsibility for the information given in the resource and ownership of this resource
   */
  Author: provenance_agent_role_Author,
  /**
   * A device used by an author to record new information, which may also be used by the author to select existing information for aggregation with newly recorded information for the purpose of generating a new artifact.
   */
  Composer: provenance_agent_role_Composer,
  /**
   * The entity that is accountable for maintaining a true an accurate copy of the original record
   */
  Custodian: provenance_agent_role_Custodian,
  /**
   * A person entering the data into the originating system
   */
  Enterer: provenance_agent_role_Enterer,
  /**
   * A person who reported information that contributed to the resource
   */
  Informant: provenance_agent_role_Informant,
  /**
   * The person authenticated the content and accepted legal responsibility for its content
   */
  LegalAuthenticator: provenance_agent_role_LegalAuthenticator,
  /**
   * A person, animal, organization or device that who actually and principally carries out the activity
   */
  Performer: provenance_agent_role_Performer,
  /**
   * A person who verifies the correctness and appropriateness of activity
   */
  Verifier: provenance_agent_role_Verifier,
};
const provenance_agent_type_Assembler: Coding = {
  code: 'assembler',
  display: 'Assembler',
  system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
};
const provenance_agent_type_Attester: Coding = {
  code: 'attester',
  display: 'Attester',
  system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
};
const provenance_agent_type_Author: Coding = {
  code: 'author',
  display: 'Author',
  system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
};
const provenance_agent_type_Composer: Coding = {
  code: 'composer',
  display: 'Composer',
  system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
};
const provenance_agent_type_Custodian: Coding = {
  code: 'custodian',
  display: 'Custodian',
  system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
};
const provenance_agent_type_Enterer: Coding = {
  code: 'enterer',
  display: 'Enterer',
  system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
};
const provenance_agent_type_Informant: Coding = {
  code: 'informant',
  display: 'Informant',
  system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
};
const provenance_agent_type_LegalAuthenticator: Coding = {
  code: 'legal',
  display: 'Legal Authenticator',
  system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
};
const provenance_agent_type_Performer: Coding = {
  code: 'performer',
  display: 'Performer',
  system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
};
const provenance_agent_type_Verifier: Coding = {
  code: 'verifier',
  display: 'Verifier',
  system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
};
/**
 * The type of participation a provenance participant.
 */
export const ProvenanceAgentType = {
  /**
   * A device that operates independently of an author on custodian's algorithms for data extraction of existing information for purpose of generating a new artifact.
   */
  Assembler: provenance_agent_type_Assembler,
  /**
   * A verifier who attests to the accuracy of the resource
   */
  Attester: provenance_agent_type_Attester,
  /**
   * A party that originates the resource and therefore has responsibility for the information given in the resource and ownership of this resource
   */
  Author: provenance_agent_type_Author,
  /**
   * A device used by an author to record new information, which may also be used by the author to select existing information for aggregation with newly recorded information for the purpose of generating a new artifact.
   */
  Composer: provenance_agent_type_Composer,
  /**
   * The entity that is accountable for maintaining a true an accurate copy of the original record
   */
  Custodian: provenance_agent_type_Custodian,
  /**
   * A person entering the data into the originating system
   */
  Enterer: provenance_agent_type_Enterer,
  /**
   * A person who reported information that contributed to the resource
   */
  Informant: provenance_agent_type_Informant,
  /**
   * The person authenticated the content and accepted legal responsibility for its content
   */
  LegalAuthenticator: provenance_agent_type_LegalAuthenticator,
  /**
   * A person, animal, organization or device that who actually and principally carries out the activity
   */
  Performer: provenance_agent_type_Performer,
  /**
   * A person who verifies the correctness and appropriateness of activity
   */
  Verifier: provenance_agent_type_Verifier,
};
const provenance_entity_role_Derivation: Coding = {
  code: 'derivation',
  display: 'Derivation',
  system: 'http://hl7.org/fhir/provenance-entity-role',
};
const provenance_entity_role_Quotation: Coding = {
  code: 'quotation',
  display: 'Quotation',
  system: 'http://hl7.org/fhir/provenance-entity-role',
};
const provenance_entity_role_Removal: Coding = {
  code: 'removal',
  display: 'Removal',
  system: 'http://hl7.org/fhir/provenance-entity-role',
};
const provenance_entity_role_Revision: Coding = {
  code: 'revision',
  display: 'Revision',
  system: 'http://hl7.org/fhir/provenance-entity-role',
};
const provenance_entity_role_Source: Coding = {
  code: 'source',
  display: 'Source',
  system: 'http://hl7.org/fhir/provenance-entity-role',
};
/**
 * How an entity was used in an activity.
 */
export const ProvenanceEntityRole = {
  /**
   * A transformation of an entity into another, an update of an entity resulting in a new one, or the construction of a new entity based on a pre-existing entity.
   */
  Derivation: provenance_entity_role_Derivation,
  /**
   * The repeat of (some or all of) an entity, such as text or image, by someone who might or might not be its original author.
   */
  Quotation: provenance_entity_role_Quotation,
  /**
   * A derivation for which the entity is removed from accessibility usually through the use of the Delete operation.
   */
  Removal: provenance_entity_role_Removal,
  /**
   * A derivation for which the resulting entity is a revised version of some original.
   */
  Revision: provenance_entity_role_Revision,
  /**
   * A primary source for a topic refers to something produced by some agent with direct experience and knowledge about the topic, at the time of the topic's study, without benefit from hindsight.
   */
  Source: provenance_entity_role_Source,
};
/**
 * Types of roles indicating how a particular agent was involved with the creation or modification of a resource for use when exposing event history
 */
export const ProvenanceHistoryAgentType = {
  /**
   * Definition: A party that originates the Act and therefore has responsibility for the information given in the Act and ownership of this Act.
   *
   *
   *                            Example: the report writer, the person writing the act definition, the guideline author, the placer of an order, the EKG cart (device) creating a report etc. Every Act should have an author. Authorship is regardless of mood always actual authorship.
   *
   *                         Examples of such policies might include:
   *
   *
   *
   *                               The author and anyone they explicitly delegate may update the report;
   *
   *
   *
   *                               All administrators within the same clinic may cancel and reschedule appointments created by other administrators within that clinic;
   *
   *
   *
   *                         A party that is neither an author nor a party who is extended authorship maintenance rights by policy, may only amend, reverse, override, replace, or follow up in other ways on this Act, whereby the Act remains intact and is linked to another Act authored by that other party.
   */
  AuthorOriginator: v3_ParticipationType_AuthorOriginator,
  /**
   * A person entering the data into the originating system.  The data entry person is collected optionally for internal quality control purposes.  This includes the transcriptionist for dictated text.
   */
  DataEntryPerson: v3_ParticipationType_DataEntryPerson,
  /**
   * A source of reported information (e.g., a next of kin who answers questions about the patient's history).  For history questions, the patient is logically an informant, yet the informant of history questions is implicitly the subject.
   */
  Informant: v3_ParticipationType_Informant,
  /**
   * A person who verifies the correctness and appropriateness of the service (plan, order, event, etc.) and hence takes on accountability.
   */
  Verifier: v3_ParticipationType_Verifier,
};
const v3_DataOperation_Abort: Coding = {
  code: 'ABORT',
  display: 'abort',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_DataOperation_Activate: Coding = {
  code: 'ACTIVATE',
  display: 'activate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_DataOperation_Cancel: Coding = {
  code: 'CANCEL',
  display: 'cancel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_DataOperation_Complete: Coding = {
  code: 'COMPLETE',
  display: 'complete',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_DataOperation_Hold: Coding = {
  code: 'HOLD',
  display: 'hold',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_DataOperation_Obsolete: Coding = {
  code: 'OBSOLETE',
  display: 'obsolete',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_DataOperation_Reactivate: Coding = {
  code: 'REACTIVATE',
  display: 'reactivate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_DataOperation_Release: Coding = {
  code: 'RELEASE',
  display: 'release',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_DataOperation_Resume: Coding = {
  code: 'RESUME',
  display: 'resume',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_DataOperation_Suspend: Coding = {
  code: 'SUSPEND',
  display: 'suspend',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
/**
 * Codes for Provenance activities that are relevant when capturing event history for resources.
 */
export const ProvenanceHistoryRecordActivity = {
  /**
   * Description:Change the status of an object representing an Act to "aborted", i.e., terminated prior to the originally intended completion. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Abort: v3_DataOperation_Abort,
  /**
   * Description:Change the status of an object representing an Act to "active", i.e., so it can be performed or is being performed, for the first time. (Contrast with REACTIVATE.) For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Activate: v3_DataOperation_Activate,
  /**
   * Description:Change the status of an object representing an Act to "cancelled", i.e., abandoned before activation. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Cancel: v3_DataOperation_Cancel,
  /**
   * Description:Change the status of an object representing an Act to "completed", i.e., terminated normally after all of its constituents have been performed. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Complete: v3_DataOperation_Complete,
  /**
   * Description:Fundamental operation in an Information System (IS) that results only in the act of bringing an object into existence. Note: The preceding definition is taken from the HL7 RBAC specification.  There is no restriction on how the operation is invoked, e.g., via a user interface. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Create: v3_DataOperation_Create,
  /**
   * Description:Fundamental operation in an Information System (IS) that results only in the removal of information about an object from memory or storage. Note: The preceding definition is taken from the HL7 RBAC specification.  There is no restriction on how the operation is invoked, e.g., via a user interface.
   */
  Delete: v3_DataOperation_Delete,
  /**
   * Description:Change the status of an object representing an Act to "held", i.e., put aside an Act that is still in preparatory stages.  No action can occur until the Act is released. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Hold: v3_DataOperation_Hold,
  /**
   * Description:Change the status of an object representing an Act to "nullified", i.e., treat as though it never existed. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Nullify: v3_DataOperation_Nullify,
  /**
   * Description:Change the status of an object representing an Act to "obsolete" when it has been replaced by a new instance. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Obsolete: v3_DataOperation_Obsolete,
  /**
   * Description:Change the status of a formerly active object representing an Act to "active", i.e., so it can again be performed or is being performed. (Contrast with ACTIVATE.) For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Reactivate: v3_DataOperation_Reactivate,
  /**
   * Description:Change the status of an object representing an Act so it is no longer "held", i.e., allow action to occur. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Release: v3_DataOperation_Release,
  /**
   * Description:Change the status of a suspended object representing an Act to "active", i.e., so it can be performed or is being performed. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Resume: v3_DataOperation_Resume,
  /**
   * Definition:Change the status of an object representing an Act to suspended, i.e., so it is temporarily not in service.
   */
  Suspend: v3_DataOperation_Suspend,
  /**
   * Definition:Fundamental operation in an Information System (IS) that results only in the revision or alteration of an object. Note: The preceding definition is taken from the HL7 RBAC specification. There is no restriction on how the operation is invoked, e.g., via a user interface.
   */
  Revise: v3_DataOperation_Revise,
};
const provider_qualification_Dentist: Coding = {
  code: '311405',
  display: 'Dentist',
  system: 'http://terminology.hl7.org/CodeSystem/ex-providerqualification',
};
const provider_qualification_Optometrist: Coding = {
  code: '604210',
  display: 'Optometrist',
  system: 'http://terminology.hl7.org/CodeSystem/ex-providerqualification',
};
const provider_qualification_Ophthalmologist: Coding = {
  code: '604215',
  display: 'Ophthalmologist',
  system: 'http://terminology.hl7.org/CodeSystem/ex-providerqualification',
};
/**
 * This value set includes sample Provider Qualification codes.
 */
export const ProviderQualification = {
  /**
   * Dentist General Practitioner (DDS, DDM).
   */
  Dentist: provider_qualification_Dentist,
  /**
   * Optometrist.
   */
  Optometrist: provider_qualification_Optometrist,
  /**
   * Ophthalmologist.
   */
  Ophthalmologist: provider_qualification_Ophthalmologist,
};
const publication_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/publication-status',
};
const publication_status_Draft: Coding = {
  code: 'draft',
  display: 'Draft',
  system: 'http://hl7.org/fhir/publication-status',
};
const publication_status_Retired: Coding = {
  code: 'retired',
  display: 'Retired',
  system: 'http://hl7.org/fhir/publication-status',
};
const publication_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/publication-status',
};
/**
 * The lifecycle status of an artifact.
 */
export const PublicationStatus = {
  /**
   * This resource is ready for normal use.
   */
  Active: publication_status_Active,
  /**
   * This resource is still under development and is not yet considered to be ready for normal use.
   */
  Draft: publication_status_Draft,
  /**
   * This resource has been withdrawn or superseded and should no longer be used.
   */
  Retired: publication_status_Retired,
  /**
   * The authoring system does not know which of the status values currently applies for this resource.  Note: This concept is not to be used for "other" - one of the listed statuses is presumed to apply, it's just not known which one.
   */
  Unknown: publication_status_Unknown,
};
const quality_type_INDELComparison: Coding = {
  code: 'indel',
  display: 'INDEL Comparison',
  system: 'http://hl7.org/fhir/quality-type',
};
const quality_type_SNPComparison: Coding = {
  code: 'snp',
  display: 'SNP Comparison',
  system: 'http://hl7.org/fhir/quality-type',
};
const quality_type_UNKNOWNComparison: Coding = {
  code: 'unknown',
  display: 'UNKNOWN Comparison',
  system: 'http://hl7.org/fhir/quality-type',
};
/**
 * Type for quality report.
 */
export const QualityType = {
  /**
   * INDEL Comparison.
   */
  INDELComparison: quality_type_INDELComparison,
  /**
   * SNP Comparison.
   */
  SNPComparison: quality_type_SNPComparison,
  /**
   * UNKNOWN Comparison.
   */
  UNKNOWNComparison: quality_type_UNKNOWNComparison,
};
const quantity_comparator_LessThan: Coding = {
  code: '<',
  display: 'Less than',
  system: 'http://hl7.org/fhir/quantity-comparator',
};
const quantity_comparator_LessOrEqualTo: Coding = {
  code: '<=',
  display: 'Less or Equal to',
  system: 'http://hl7.org/fhir/quantity-comparator',
};
const quantity_comparator_GreaterThan: Coding = {
  code: '>',
  display: 'Greater than',
  system: 'http://hl7.org/fhir/quantity-comparator',
};
const quantity_comparator_GreaterOrEqualTo: Coding = {
  code: '>=',
  display: 'Greater or Equal to',
  system: 'http://hl7.org/fhir/quantity-comparator',
};
/**
 * How the Quantity should be understood and represented.
 */
export const QuantityComparator = {
  /**
   * The actual value is less than the given value.
   */
  LessThan: quantity_comparator_LessThan,
  /**
   * The actual value is less than or equal to the given value.
   */
  LessOrEqualTo: quantity_comparator_LessOrEqualTo,
  /**
   * The actual value is greater than the given value.
   */
  GreaterThan: quantity_comparator_GreaterThan,
  /**
   * The actual value is greater than or equal to the given value.
   */
  GreaterOrEqualTo: quantity_comparator_GreaterOrEqualTo,
};
const question_max_occurs_Repeating: Coding = {
  code: '*',
  display: 'Repeating',
  system: 'http://terminology.hl7.org/CodeSystem/question-max-occurs',
};
/**
 * Flags an element as having unlimited repetitions.
 */
export const QuestionMaxOccurs = {
  /**
   * Element can repeat an unlimited number of times.
   */
  Repeating: question_max_occurs_Repeating,
};
const questionnaire_answers_status_Amended: Coding = {
  code: 'amended',
  display: 'Amended',
  system: 'http://hl7.org/fhir/questionnaire-answers-status',
};
const questionnaire_answers_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/questionnaire-answers-status',
};
const questionnaire_answers_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/questionnaire-answers-status',
};
const questionnaire_answers_status_InProgress: Coding = {
  code: 'in-progress',
  display: 'In Progress',
  system: 'http://hl7.org/fhir/questionnaire-answers-status',
};
const questionnaire_answers_status_Stopped: Coding = {
  code: 'stopped',
  display: 'Stopped',
  system: 'http://hl7.org/fhir/questionnaire-answers-status',
};
/**
 * Lifecycle status of the questionnaire response.
 */
export const QuestionnaireAnswersStatus = {
  /**
   * This QuestionnaireResponse has been filled out with answers, then marked as complete, yet changes or additions have been made to it afterwards.
   */
  Amended: questionnaire_answers_status_Amended,
  /**
   * This QuestionnaireResponse has been filled out with answers and the current content is regarded as definitive.
   */
  Completed: questionnaire_answers_status_Completed,
  /**
   * This QuestionnaireResponse was entered in error and voided.
   */
  EnteredInError: questionnaire_answers_status_EnteredInError,
  /**
   * This QuestionnaireResponse has been partially filled out with answers but changes or additions are still expected to be made to it.
   */
  InProgress: questionnaire_answers_status_InProgress,
  /**
   * This QuestionnaireResponse has been partially filled out with answers but has been abandoned. It is unknown whether changes or additions are expected to be made to it.
   */
  Stopped: questionnaire_answers_status_Stopped,
};
const QuestionnaireCategory_ConsumerSatisfactionQuestionnaire: Coding = {
  code: '273384008',
  display: 'Consumer satisfaction questionnaire',
  system: 'http://snomed.info/sct',
};
const QuestionnaireCategory_GeneralHealthQuestionnaire: Coding = {
  code: '273479001',
  display: 'General health questionnaire',
  system: 'http://snomed.info/sct',
};
const QuestionnaireCategory_MentalStatusQuestionnaire: Coding = {
  code: '273601006',
  display: 'Mental status questionnaire',
  system: 'http://snomed.info/sct',
};
const QuestionnaireCategory_SocialSupportQuestionnaire: Coding = {
  code: '273820006',
  display: 'Social support questionnaire',
  system: 'http://snomed.info/sct',
};
const QuestionnaireCategory_AdministrativeForm: Coding = {
  code: '304784009',
  display: 'Administrative form',
  system: 'http://snomed.info/sct',
};
/**
 * Example list of potential categories for questionnaires.
 */
export const QuestionnaireCategory = {
  ConsumerSatisfactionQuestionnaire: QuestionnaireCategory_ConsumerSatisfactionQuestionnaire,
  GeneralHealthQuestionnaire: QuestionnaireCategory_GeneralHealthQuestionnaire,
  MentalStatusQuestionnaire: QuestionnaireCategory_MentalStatusQuestionnaire,
  SocialSupportQuestionnaire: QuestionnaireCategory_SocialSupportQuestionnaire,
  AdministrativeForm: QuestionnaireCategory_AdministrativeForm,
};
const questionnaire_display_category_Help: Coding = {
  code: 'help',
  display: 'Help',
  system: 'http://hl7.org/fhir/questionnaire-display-category',
};
const questionnaire_display_category_Instructions: Coding = {
  code: 'instructions',
  display: 'Instructions',
  system: 'http://hl7.org/fhir/questionnaire-display-category',
};
const questionnaire_display_category_Security: Coding = {
  code: 'security',
  display: 'Security',
  system: 'http://hl7.org/fhir/questionnaire-display-category',
};
/**
 * Codes defining the purpose of a Questionnaire item of type 'text'.
 */
export const QuestionnaireDisplayCategory = {
  /**
   * The text provides additional guidance on populating the containing item.  Help text isn't necessarily expected to be rendered as part of the form, but may instead be made available through fly-over, pop-up button, link to a "help" page, etc.
   */
  Help: questionnaire_display_category_Help,
  /**
   * The text provides guidance on how to populate or use a portion of the questionnaire (or the questionnaire as a whole).
   */
  Instructions: questionnaire_display_category_Instructions,
  /**
   * The text provides guidance on how the information should be or will be handled from a security/confidentiality/access control perspective when completed
   */
  Security: questionnaire_display_category_Security,
};
const questionnaire_enable_behavior_All: Coding = {
  code: 'all',
  display: 'All',
  system: 'http://hl7.org/fhir/questionnaire-enable-behavior',
};
const questionnaire_enable_behavior_Any: Coding = {
  code: 'any',
  display: 'Any',
  system: 'http://hl7.org/fhir/questionnaire-enable-behavior',
};
/**
 * Controls how multiple enableWhen values are interpreted -  whether all or any must be true.
 */
export const QuestionnaireEnableBehavior = {
  /**
   * Enable the question when all the enableWhen criteria are satisfied.
   */
  All: questionnaire_enable_behavior_All,
  /**
   * Enable the question when any of the enableWhen criteria are satisfied.
   */
  Any: questionnaire_enable_behavior_Any,
};
const questionnaire_enable_operator_NotEquals: Coding = {
  code: '!=',
  display: 'Not Equals',
  system: 'http://hl7.org/fhir/questionnaire-enable-operator',
};
const questionnaire_enable_operator_LessThan: Coding = {
  code: '<',
  display: 'Less Than',
  system: 'http://hl7.org/fhir/questionnaire-enable-operator',
};
const questionnaire_enable_operator_LessOrEquals: Coding = {
  code: '<=',
  display: 'Less or Equals',
  system: 'http://hl7.org/fhir/questionnaire-enable-operator',
};
const questionnaire_enable_operator_Equals: Coding = {
  code: '=',
  display: 'Equals',
  system: 'http://hl7.org/fhir/questionnaire-enable-operator',
};
const questionnaire_enable_operator_GreaterThan: Coding = {
  code: '>',
  display: 'Greater Than',
  system: 'http://hl7.org/fhir/questionnaire-enable-operator',
};
const questionnaire_enable_operator_GreaterOrEquals: Coding = {
  code: '>=',
  display: 'Greater or Equals',
  system: 'http://hl7.org/fhir/questionnaire-enable-operator',
};
const questionnaire_enable_operator_Exists: Coding = {
  code: 'exists',
  display: 'Exists',
  system: 'http://hl7.org/fhir/questionnaire-enable-operator',
};
/**
 * The criteria by which a question is enabled.
 */
export const QuestionnaireEnableOperator = {
  /**
   * True if whether at least no answer has a value that is equal to the enableWhen answer.
   */
  NotEquals: questionnaire_enable_operator_NotEquals,
  /**
   * True if whether at least no answer has a value that is less than the enableWhen answer.
   */
  LessThan: questionnaire_enable_operator_LessThan,
  /**
   * True if whether at least no answer has a value that is less or equal to the enableWhen answer.
   */
  LessOrEquals: questionnaire_enable_operator_LessOrEquals,
  /**
   * True if whether at least one answer has a value that is equal to the enableWhen answer.
   */
  Equals: questionnaire_enable_operator_Equals,
  /**
   * True if whether at least no answer has a value that is greater than the enableWhen answer.
   */
  GreaterThan: questionnaire_enable_operator_GreaterThan,
  /**
   * True if whether at least no answer has a value that is greater or equal to the enableWhen answer.
   */
  GreaterOrEquals: questionnaire_enable_operator_GreaterOrEquals,
  /**
   * True if whether an answer exists is equal to the enableWhen answer (which must be a boolean).
   */
  Exists: questionnaire_enable_operator_Exists,
};
const questionnaire_item_control_AnswerTable: Coding = {
  code: 'atable',
  display: 'Answer Table',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_AutoComplete: Coding = {
  code: 'autocomplete',
  display: 'Auto-complete',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_CheckBox: Coding = {
  code: 'check-box',
  display: 'Check-box',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_DropDown: Coding = {
  code: 'drop-down',
  display: 'Drop down',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_FlyOver: Coding = {
  code: 'flyover',
  display: 'Fly-over',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_Footer: Coding = {
  code: 'footer',
  display: 'Footer',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_Group: Coding = {
  code: 'group',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_GroupTable: Coding = {
  code: 'gtable',
  display: 'Group Table',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_Header: Coding = {
  code: 'header',
  display: 'Header',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_HelpButton: Coding = {
  code: 'help',
  display: 'Help-Button',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_HorizontalAnswerTable: Coding = {
  code: 'htable',
  display: 'Horizontal Answer Table',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_InLine: Coding = {
  code: 'inline',
  display: 'In-line',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_List: Coding = {
  code: 'list',
  display: 'List',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_Lookup: Coding = {
  code: 'lookup',
  display: 'Lookup',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_LowerBound: Coding = {
  code: 'lower',
  display: 'Lower-bound',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_Prompt: Coding = {
  code: 'prompt',
  display: 'Prompt',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_Question: Coding = {
  code: 'question',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_RadioButton: Coding = {
  code: 'radio-button',
  display: 'Radio Button',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_Slider: Coding = {
  code: 'slider',
  display: 'Slider',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_Spinner: Coding = {
  code: 'spinner',
  display: 'Spinner',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_VerticalAnswerTable: Coding = {
  code: 'table',
  display: 'Vertical Answer Table',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_Text: Coding = {
  code: 'text',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_TextBox: Coding = {
  code: 'text-box',
  display: 'Text Box',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_Unit: Coding = {
  code: 'unit',
  display: 'Unit',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
const questionnaire_item_control_UpperBound: Coding = {
  code: 'upper',
  display: 'Upper-bound',
  system: 'http://hl7.org/fhir/questionnaire-item-control',
};
/**
 * Starter set of user interface control/display mechanisms that might be used when rendering an item in a questionnaire.
 */
export const QuestionnaireItemControl = {
  /**
   * This table has one row - for the question.  Permitted answers are columns.  Used for choice questions.
   */
  AnswerTable: questionnaire_item_control_AnswerTable,
  /**
   * A control which provides a list of potential matches based on text entered into a control.  Used for large choice sets where text-matching is an appropriate discovery mechanism.
   */
  AutoComplete: questionnaire_item_control_AutoComplete,
  /**
   * A control where choices are listed with a box beside them.  The box can be toggled to select or de-select a given choice.  Multiple selections may be possible.
   */
  CheckBox: questionnaire_item_control_CheckBox,
  /**
   * A control where an item (or multiple items) can be selected from a list that is only displayed when the user is editing the field.
   */
  DropDown: questionnaire_item_control_DropDown,
  /**
   * Text is temporarily visible over top of an item if the mouse is positioned over top of the text for the containing item
   */
  FlyOver: questionnaire_item_control_FlyOver,
  /**
   * The group is to be continuously visible at the bottom of the questionnaire
   */
  Footer: questionnaire_item_control_Footer,
  /**
   * UI controls relevant to organizing groups of questions
   */
  Group: questionnaire_item_control_Group,
  /**
   * Questions within the group are columns in the table with each group repetition as a row.  Used for single-answer questions.
   */
  GroupTable: questionnaire_item_control_GroupTable,
  /**
   * The group is to be continuously visible at the top of the questionnaire
   */
  Header: questionnaire_item_control_Header,
  /**
   * Text is displayed in a dialog box or similar control if invoked by pushing a button or some other UI-appropriate action to request 'help' for a question, group or the questionnaire as a whole (depending what the text is nested within)
   */
  HelpButton: questionnaire_item_control_HelpButton,
  /**
   * Questions within the group are columns in the table with possible answers as rows.  Used for 'choice' questions.
   */
  HorizontalAnswerTable: questionnaire_item_control_HorizontalAnswerTable,
  /**
   * Text is displayed as a paragraph in a sequential position between sibling items (default behavior)
   */
  InLine: questionnaire_item_control_InLine,
  /**
   * Questions within the group should be listed sequentially
   */
  List: questionnaire_item_control_List,
  /**
   * A control where editing an item spawns a separate dialog box or screen permitting a user to navigate, filter or otherwise discover an appropriate match.  Useful for large choice sets where text matching is not an appropriate discovery mechanism.  Such screens must generally be tuned for the specific choice list structure.
   */
  Lookup: questionnaire_item_control_Lookup,
  /**
   * Text is displayed to the left of the set of answer choices or a scaling control for the parent question item to indicate the meaning of the 'lower' bound.  E.g. 'Strongly disagree'
   */
  LowerBound: questionnaire_item_control_LowerBound,
  /**
   * Text is displayed immediately below or within the answer-entry area of the containing question item (typically as a guide for what to enter)
   */
  Prompt: questionnaire_item_control_Prompt,
  /**
   * UI controls relevant to capturing question data
   */
  Question: questionnaire_item_control_Question,
  /**
   * A control where choices are listed with a button beside them.  The button can be toggled to select or de-select a given choice.  Selecting one item deselects all others.
   */
  RadioButton: questionnaire_item_control_RadioButton,
  /**
   * A control where an axis is displayed between the high and low values and the control can be visually manipulated to select a value anywhere on the axis.
   */
  Slider: questionnaire_item_control_Slider,
  /**
   * A control where a list of numeric or other ordered values can be scrolled through via arrows.
   */
  Spinner: questionnaire_item_control_Spinner,
  /**
   * Questions within the group are rows in the table with possible answers as columns.  Used for 'choice' questions.
   */
  VerticalAnswerTable: questionnaire_item_control_VerticalAnswerTable,
  /**
   * UI controls relevant to rendering questionnaire text items
   */
  Text: questionnaire_item_control_Text,
  /**
   * A control where a user can type in their answer freely.
   */
  TextBox: questionnaire_item_control_TextBox,
  /**
   * Text is displayed adjacent (horizontally or vertically) to the answer space for the parent question, typically to indicate a unit of measure
   */
  Unit: questionnaire_item_control_Unit,
  /**
   * Text is displayed to the right of the set of answer choices or a scaling control for the parent question item to indicate the meaning of the 'upper' bound.  E.g. 'Strongly agree'
   */
  UpperBound: questionnaire_item_control_UpperBound,
};
const v3_ParticipationMode_ElectronicData: Coding = {
  code: 'ELECTRONIC',
  display: 'electronic data',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
const v3_ParticipationMode_Verbal: Coding = {
  code: 'VERBAL',
  display: 'verbal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
const v3_ParticipationMode_Written: Coding = {
  code: 'WRITTEN',
  display: 'written',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
/**
 * Codes describing how the QuestionnaireResponse was populated
 */
export const QuestionnaireresponseMode = {
  /**
   * Participation by non-human-languaged based electronic signal
   */
  ElectronicData: v3_ParticipationMode_ElectronicData,
  /**
   * Participation by voice communication
   */
  Verbal: v3_ParticipationMode_Verbal,
  /**
   * Participation by human language recorded on a physical material
   */
  Written: v3_ParticipationMode_Written,
};
const questionnaire_usage_mode_CaptureOnly: Coding = {
  code: 'capture',
  display: 'Capture Only',
  system: 'http://terminology.hl7.org/CodeSystem/questionnaire-usage-mode',
};
const questionnaire_usage_mode_CaptureAndDisplay: Coding = {
  code: 'capture-display',
  display: 'Capture & Display',
  system: 'http://terminology.hl7.org/CodeSystem/questionnaire-usage-mode',
};
const questionnaire_usage_mode_CaptureOrIfAnsweredDisplay: Coding = {
  code: 'capture-display-non-empty',
  display: 'Capture or, if answered, Display',
  system: 'http://terminology.hl7.org/CodeSystem/questionnaire-usage-mode',
};
const questionnaire_usage_mode_DisplayOnly: Coding = {
  code: 'display',
  display: 'Display Only',
  system: 'http://terminology.hl7.org/CodeSystem/questionnaire-usage-mode',
};
const questionnaire_usage_mode_DisplayWhenAnswered: Coding = {
  code: 'display-non-empty',
  display: 'Display when Answered',
  system: 'http://terminology.hl7.org/CodeSystem/questionnaire-usage-mode',
};
/**
 * Identifies the modes of usage of a questionnaire that should enable a particular questionnaire item.
 */
export const QuestionnaireUsageMode = {
  /**
   * Render the item only when capturing data.
   */
  CaptureOnly: questionnaire_usage_mode_CaptureOnly,
  /**
   * Render the item regardless of usage mode.
   */
  CaptureAndDisplay: questionnaire_usage_mode_CaptureAndDisplay,
  /**
   * Render the item when capturing data or when displaying a completed form and the item has been answered (or has child items that have been answered).
   */
  CaptureOrIfAnsweredDisplay: questionnaire_usage_mode_CaptureOrIfAnsweredDisplay,
  /**
   * Render the item only when displaying a completed form.
   */
  DisplayOnly: questionnaire_usage_mode_DisplayOnly,
  /**
   * Render the item only when displaying a completed form and the item has been answered (or has child items that have been answered).
   */
  DisplayWhenAnswered: questionnaire_usage_mode_DisplayWhenAnswered,
};
const reaction_event_certainty_Confirmed: Coding = {
  code: 'confirmed',
  display: 'Confirmed',
  system: 'http://terminology.hl7.org/CodeSystem/reaction-event-certainty',
};
const reaction_event_certainty_Likely: Coding = {
  code: 'likely',
  display: 'Likely',
  system: 'http://terminology.hl7.org/CodeSystem/reaction-event-certainty',
};
const reaction_event_certainty_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/reaction-event-certainty',
};
const reaction_event_certainty_Unlikely: Coding = {
  code: 'unlikely',
  display: 'Unlikely',
  system: 'http://terminology.hl7.org/CodeSystem/reaction-event-certainty',
};
/**
 * Statement about the degree of clinical certainty that a specific substance was the cause of the manifestation in a reaction event.
 */
export const ReactionEventCertainty = {
  /**
   * There is a very high level of clinical certainty that the reaction was due to the identified substance, which may include clinical evidence by testing or rechallenge.
   */
  Confirmed: reaction_event_certainty_Confirmed,
  /**
   * There is a high level of clinical certainty that the reaction was caused by the identified substance.
   */
  Likely: reaction_event_certainty_Likely,
  /**
   * The clinical certainty that the reaction was caused by the identified substance is unknown.  It is an explicit assertion that certainty is not known.
   */
  Unknown: reaction_event_certainty_Unknown,
  /**
   * There is a low level of clinical certainty that the reaction was caused by the identified substance.
   */
  Unlikely: reaction_event_certainty_Unlikely,
};
const reaction_event_severity_Mild: Coding = {
  code: 'mild',
  display: 'Mild',
  system: 'http://hl7.org/fhir/reaction-event-severity',
};
const reaction_event_severity_Moderate: Coding = {
  code: 'moderate',
  display: 'Moderate',
  system: 'http://hl7.org/fhir/reaction-event-severity',
};
const reaction_event_severity_Severe: Coding = {
  code: 'severe',
  display: 'Severe',
  system: 'http://hl7.org/fhir/reaction-event-severity',
};
/**
 * Clinical assessment of the severity of a reaction event as a whole, potentially considering multiple different manifestations.
 */
export const ReactionEventSeverity = {
  /**
   * Causes mild physiological effects.
   */
  Mild: reaction_event_severity_Mild,
  /**
   * Causes moderate physiological effects.
   */
  Moderate: reaction_event_severity_Moderate,
  /**
   * Causes severe physiological effects.
   */
  Severe: reaction_event_severity_Severe,
};
const reason_medication_given_codes_None: Coding = {
  code: 'a',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/reason-medication-given',
};
const reason_medication_given_codes_GivenAsOrdered: Coding = {
  code: 'b',
  display: 'Given as Ordered',
  system: 'http://terminology.hl7.org/CodeSystem/reason-medication-given',
};
const reason_medication_given_codes_Emergency: Coding = {
  code: 'c',
  display: 'Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/reason-medication-given',
};
/**
 * This value set is provided as an example. The value set to instantiate this attribute should be drawn from a robust terminology code system that consists of or contains concepts to support the medication process.
 */
export const ReasonMedicationGivenCodes = {
  /**
   * No reason known.
   */
  None: reason_medication_given_codes_None,
  /**
   * The administration was following an ordered protocol.
   */
  GivenAsOrdered: reason_medication_given_codes_GivenAsOrdered,
  /**
   * The administration was needed to treat an emergency.
   */
  Emergency: reason_medication_given_codes_Emergency,
};
const recommendation_strength_Strong: Coding = {
  code: 'strong',
  display: 'Strong',
  system: 'http://terminology.hl7.org/CodeSystem/recommendation-strength',
};
const recommendation_strength_Weak: Coding = {
  code: 'weak',
  display: 'Weak',
  system: 'http://terminology.hl7.org/CodeSystem/recommendation-strength',
};
/**
 * A rating system that describes the strength of the recommendation, such as the GRADE, DynaMed, or HGPS systems.
 */
export const RecommendationStrength = {
  /**
   * Strong recommendation.
   */
  Strong: recommendation_strength_Strong,
  /**
   * Weak recommendation.
   */
  Weak: recommendation_strength_Weak,
};
const reference_handling_policy_ReferenceIntegrityEnforced: Coding = {
  code: 'enforced',
  display: 'Reference Integrity Enforced',
  system: 'http://hl7.org/fhir/reference-handling-policy',
};
const reference_handling_policy_LiteralReferences: Coding = {
  code: 'literal',
  display: 'Literal References',
  system: 'http://hl7.org/fhir/reference-handling-policy',
};
const reference_handling_policy_LocalReferencesOnly: Coding = {
  code: 'local',
  display: 'Local References Only',
  system: 'http://hl7.org/fhir/reference-handling-policy',
};
const reference_handling_policy_LogicalReferences: Coding = {
  code: 'logical',
  display: 'Logical References',
  system: 'http://hl7.org/fhir/reference-handling-policy',
};
const reference_handling_policy_ResolvesReferences: Coding = {
  code: 'resolves',
  display: 'Resolves References',
  system: 'http://hl7.org/fhir/reference-handling-policy',
};
/**
 * A set of flags that defines how references are supported.
 */
export const ReferenceHandlingPolicy = {
  /**
   * The server enforces that references have integrity - e.g. it ensures that references can always be resolved. This is typically the case for clinical record systems, but often not the case for middleware/proxy systems.
   */
  ReferenceIntegrityEnforced: reference_handling_policy_ReferenceIntegrityEnforced,
  /**
   * The server supports and populates Literal references (i.e. using Reference.reference) where they are known (this code does not guarantee that all references are literal; see 'enforced').
   */
  LiteralReferences: reference_handling_policy_LiteralReferences,
  /**
   * The server does not support references that point to other servers.
   */
  LocalReferencesOnly: reference_handling_policy_LocalReferencesOnly,
  /**
   * The server allows logical references (i.e. using Reference.identifier).
   */
  LogicalReferences: reference_handling_policy_LogicalReferences,
  /**
   * The server will attempt to resolve logical references to literal references - i.e. converting Reference.identifier to Reference.reference (if resolution fails, the server may still accept resources; see logical).
   */
  ResolvesReferences: reference_handling_policy_ResolvesReferences,
};
const ReferencerangeAppliesto_VAL248152002: Coding = {
  code: '248152002',
  system: 'http://snomed.info/sct',
};
const ReferencerangeAppliesto_VAL248153007: Coding = {
  code: '248153007',
  system: 'http://snomed.info/sct',
};
/**
 * This value set defines a set of codes that can be used to indicate the particular target population the reference range applies to.
 */
export const ReferencerangeAppliesto = {
  VAL248152002: ReferencerangeAppliesto_VAL248152002,
  VAL248153007: ReferencerangeAppliesto_VAL248153007,
  VAL77386006: ImmunizationRecommendationReason_VAL77386006,
};
const referencerange_meaning_Endocrine: Coding = {
  code: 'endocrine',
  display: 'Endocrine',
  system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
};
const referencerange_meaning_FollicularStage: Coding = {
  code: 'follicular',
  display: 'Follicular Stage',
  system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
};
const referencerange_meaning_Luteal: Coding = {
  code: 'luteal',
  display: 'Luteal',
  system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
};
const referencerange_meaning_MidCycle: Coding = {
  code: 'midcycle',
  display: 'MidCycle',
  system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
};
const referencerange_meaning_NormalRange: Coding = {
  code: 'normal',
  display: 'Normal Range',
  system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
};
const referencerange_meaning_PostTherapeuticDesiredLevel: Coding = {
  code: 'post',
  display: 'Post Therapeutic Desired Level',
  system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
};
const referencerange_meaning_PostMenopause: Coding = {
  code: 'postmenopausal',
  display: 'Post-Menopause',
  system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
};
const referencerange_meaning_PreTherapeuticDesiredLevel: Coding = {
  code: 'pre',
  display: 'Pre Therapeutic Desired Level',
  system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
};
const referencerange_meaning_PrePuberty: Coding = {
  code: 'pre-puberty',
  display: 'Pre-Puberty',
  system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
};
const referencerange_meaning_RecommendedRange: Coding = {
  code: 'recommended',
  display: 'Recommended Range',
  system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
};
const referencerange_meaning_TherapeuticDesiredLevel: Coding = {
  code: 'therapeutic',
  display: 'Therapeutic Desired Level',
  system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
};
const referencerange_meaning_TreatmentRange: Coding = {
  code: 'treatment',
  display: 'Treatment Range',
  system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
};
const referencerange_meaning_Type: Coding = {
  code: 'type',
  display: 'Type',
  system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
};
/**
 * This value set defines a set of codes that can be used to indicate the meaning/use of a reference range for a particular target population.
 */
export const ReferencerangeMeaning = {
  /**
   * Endocrine related states that change the expected value.
   */
  Endocrine: referencerange_meaning_Endocrine,
  /**
   * An expected range in an individual during the follicular stage of the cycle.
   */
  FollicularStage: referencerange_meaning_FollicularStage,
  /**
   * An expected range in an individual during the luteal stage of the cycle.
   */
  Luteal: referencerange_meaning_Luteal,
  /**
   * An expected range in an individual during the midcycle stage of the cycle.
   */
  MidCycle: referencerange_meaning_MidCycle,
  /**
   * Values expected for a normal member of the relevant control population being measured. Typically each results producer such as a laboratory has specific normal ranges and they are usually defined as within two standard deviations from the mean and account for 95.45% of this population.
   */
  NormalRange: referencerange_meaning_NormalRange,
  /**
   * The optimal range for best therapeutic outcomes for a specimen taken immediately after administration.
   */
  PostTherapeuticDesiredLevel: referencerange_meaning_PostTherapeuticDesiredLevel,
  /**
   * An expected range in an individual post-menopause.
   */
  PostMenopause: referencerange_meaning_PostMenopause,
  /**
   * The optimal range for best therapeutic outcomes for a specimen taken immediately before administration.
   */
  PreTherapeuticDesiredLevel: referencerange_meaning_PreTherapeuticDesiredLevel,
  /**
   * An expected range in an individual prior to puberty.
   */
  PrePuberty: referencerange_meaning_PrePuberty,
  /**
   * The range that is recommended by a relevant professional body.
   */
  RecommendedRange: referencerange_meaning_RecommendedRange,
  /**
   * The optimal range for best therapeutic outcomes.
   */
  TherapeuticDesiredLevel: referencerange_meaning_TherapeuticDesiredLevel,
  /**
   * The range at which treatment would/should be considered.
   */
  TreatmentRange: referencerange_meaning_TreatmentRange,
  /**
   * General types of reference range.
   */
  Type: referencerange_meaning_Type,
};
const reference_version_rules_EitherSpecificOrIndependent: Coding = {
  code: 'either',
  display: 'Either Specific or independent',
  system: 'http://hl7.org/fhir/reference-version-rules',
};
const reference_version_rules_VersionIndependent: Coding = {
  code: 'independent',
  display: 'Version independent',
  system: 'http://hl7.org/fhir/reference-version-rules',
};
const reference_version_rules_VersionSpecific: Coding = {
  code: 'specific',
  display: 'Version Specific',
  system: 'http://hl7.org/fhir/reference-version-rules',
};
/**
 * Whether a reference needs to be version specific or version independent, or whether either can be used.
 */
export const ReferenceVersionRules = {
  /**
   * The reference may be either version independent or version specific.
   */
  EitherSpecificOrIndependent: reference_version_rules_EitherSpecificOrIndependent,
  /**
   * The reference must be version independent.
   */
  VersionIndependent: reference_version_rules_VersionIndependent,
  /**
   * The reference must be version specific.
   */
  VersionSpecific: reference_version_rules_VersionSpecific,
};
const rejection_criteria_BrokenSpecimenContainer: Coding = {
  code: 'broken',
  display: 'broken specimen container',
  system: 'http://terminology.hl7.org/CodeSystem/rejection-criteria',
};
const rejection_criteria_SpecimenClotted: Coding = {
  code: 'clotted',
  display: 'specimen clotted',
  system: 'http://terminology.hl7.org/CodeSystem/rejection-criteria',
};
const rejection_criteria_HemolizedSpecimen: Coding = {
  code: 'hemolized',
  display: 'hemolized specimen',
  system: 'http://terminology.hl7.org/CodeSystem/rejection-criteria',
};
const rejection_criteria_InsufficientSpecimenVolume: Coding = {
  code: 'insufficient',
  display: 'insufficient specimen volume',
  system: 'http://terminology.hl7.org/CodeSystem/rejection-criteria',
};
const rejection_criteria_SpecimenTemperatureInappropriate: Coding = {
  code: 'wrong-temperature',
  display: 'specimen temperature inappropriate',
  system: 'http://terminology.hl7.org/CodeSystem/rejection-criteria',
};
/**
 * Criterion for rejection of the specimen by laboratory.
 */
export const RejectionCriteria = {
  /**
   * specimen container broken.
   */
  BrokenSpecimenContainer: rejection_criteria_BrokenSpecimenContainer,
  /**
   * specimen clotted.
   */
  SpecimenClotted: rejection_criteria_SpecimenClotted,
  /**
   * blood specimen hemolized.
   */
  HemolizedSpecimen: rejection_criteria_HemolizedSpecimen,
  /**
   * insufficient quantity of specimen.
   */
  InsufficientSpecimenVolume: rejection_criteria_InsufficientSpecimenVolume,
  /**
   * specimen temperature inappropriate.
   */
  SpecimenTemperatureInappropriate: rejection_criteria_SpecimenTemperatureInappropriate,
};
const related_artifact_type_Citation: Coding = {
  code: 'citation',
  display: 'Citation',
  system: 'http://hl7.org/fhir/related-artifact-type',
};
const related_artifact_type_ComposedOf: Coding = {
  code: 'composed-of',
  display: 'Composed Of',
  system: 'http://hl7.org/fhir/related-artifact-type',
};
const related_artifact_type_DependsOn: Coding = {
  code: 'depends-on',
  display: 'Depends On',
  system: 'http://hl7.org/fhir/related-artifact-type',
};
const related_artifact_type_DerivedFrom: Coding = {
  code: 'derived-from',
  display: 'Derived From',
  system: 'http://hl7.org/fhir/related-artifact-type',
};
const related_artifact_type_Documentation: Coding = {
  code: 'documentation',
  display: 'Documentation',
  system: 'http://hl7.org/fhir/related-artifact-type',
};
const related_artifact_type_Justification: Coding = {
  code: 'justification',
  display: 'Justification',
  system: 'http://hl7.org/fhir/related-artifact-type',
};
const related_artifact_type_Predecessor: Coding = {
  code: 'predecessor',
  display: 'Predecessor',
  system: 'http://hl7.org/fhir/related-artifact-type',
};
const related_artifact_type_Successor: Coding = {
  code: 'successor',
  display: 'Successor',
  system: 'http://hl7.org/fhir/related-artifact-type',
};
/**
 * The type of relationship to the related artifact.
 */
export const RelatedArtifactType = {
  /**
   * Bibliographic citation for papers, references, or other relevant material for the knowledge resource. This is intended to allow for citation of related material, but that was not necessarily specifically prepared in connection with this knowledge resource.
   */
  Citation: related_artifact_type_Citation,
  /**
   * The knowledge resource is composed of the given related artifact.
   */
  ComposedOf: related_artifact_type_ComposedOf,
  /**
   * The knowledge resource depends on the given related artifact.
   */
  DependsOn: related_artifact_type_DependsOn,
  /**
   * The knowledge resource is derived from the related artifact. This is intended to capture the relationship in which a particular knowledge resource is based on the content of another artifact, but is modified to capture either a different set of overall requirements, or a more specific set of requirements such as those involved in a particular institution or clinical setting.
   */
  DerivedFrom: related_artifact_type_DerivedFrom,
  /**
   * Additional documentation for the knowledge resource. This would include additional instructions on usage as well as additional information on clinical context or appropriateness.
   */
  Documentation: related_artifact_type_Documentation,
  /**
   * A summary of the justification for the knowledge resource including supporting evidence, relevant guidelines, or other clinically important information. This information is intended to provide a way to make the justification for the knowledge resource available to the consumer of interventions or results produced by the knowledge resource.
   */
  Justification: related_artifact_type_Justification,
  /**
   * The previous version of the knowledge resource.
   */
  Predecessor: related_artifact_type_Predecessor,
  /**
   * The next version of the knowledge resource.
   */
  Successor: related_artifact_type_Successor,
};
const related_claim_relationship_AssociatedClaim: Coding = {
  code: 'associated',
  display: 'Associated Claim',
  system: 'http://terminology.hl7.org/CodeSystem/ex-relatedclaimrelationship',
};
const related_claim_relationship_PriorClaim: Coding = {
  code: 'prior',
  display: 'Prior Claim',
  system: 'http://terminology.hl7.org/CodeSystem/ex-relatedclaimrelationship',
};
/**
 * This value set includes sample Related Claim Relationship codes.
 */
export const RelatedClaimRelationship = {
  /**
   * A claim for a different suite of services which is related the suite claimed here.
   */
  AssociatedClaim: related_claim_relationship_AssociatedClaim,
  /**
   * A prior claim instance for the same intended suite of services.
   */
  PriorClaim: related_claim_relationship_PriorClaim,
};
const v3_RoleCode_PersonalRelationshipRoleType: Coding = {
  code: '_PersonalRelationshipRoleType',
  display: 'PersonalRelationshipRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Aunt: Coding = {
  code: 'AUNT',
  display: 'aunt',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Brother: Coding = {
  code: 'BRO',
  display: 'brother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_BrotherInLaw: Coding = {
  code: 'BROINLAW',
  display: 'brother-in-law',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v2_0131_EmergencyContact: Coding = {
  code: 'C',
  display: 'Emergency Contact',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0131',
};
const v3_RoleCode_Child: Coding = {
  code: 'CHILD',
  display: 'child',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AdoptedChild: Coding = {
  code: 'CHLDADOPT',
  display: 'adopted child',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FosterChild: Coding = {
  code: 'CHLDFOST',
  display: 'foster child',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ChildInLaw: Coding = {
  code: 'CHLDINLAW',
  display: 'child-in-law',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Cousin: Coding = {
  code: 'COUSN',
  display: 'cousin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NaturalDaughter: Coding = {
  code: 'DAU',
  display: 'natural daughter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AdoptedDaughter: Coding = {
  code: 'DAUADOPT',
  display: 'adopted daughter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Daughter: Coding = {
  code: 'DAUC',
  display: 'daughter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FosterDaughter: Coding = {
  code: 'DAUFOST',
  display: 'foster daughter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_DaughterInLaw: Coding = {
  code: 'DAUINLAW',
  display: 'daughter in-law',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_DomesticPartner: Coding = {
  code: 'DOMPART',
  display: 'domestic partner',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v2_0131_Employer: Coding = {
  code: 'E',
  display: 'Employer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0131',
};
const v3_RoleCode_ExtendedFamilyMember: Coding = {
  code: 'EXT',
  display: 'extended family member',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v2_0131_FederalAgency: Coding = {
  code: 'F',
  display: 'Federal Agency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0131',
};
const v3_RoleCode_FamilyMember: Coding = {
  code: 'FAMMEMB',
  display: 'family member',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FormerSpouse: Coding = {
  code: 'FMRSPS',
  display: 'former spouse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_UnrelatedFriend: Coding = {
  code: 'FRND',
  display: 'unrelated friend',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FatherInLaw: Coding = {
  code: 'FTHINLAW',
  display: 'father-in-law',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FraternalTwinBrother: Coding = {
  code: 'FTWINBRO',
  display: 'fraternal twin brother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FraternalTwinSister: Coding = {
  code: 'FTWINSIS',
  display: 'fraternal twin sister',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_GreatGrandfather: Coding = {
  code: 'GGRFTH',
  display: 'great grandfather',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_GreatGrandmother: Coding = {
  code: 'GGRMTH',
  display: 'great grandmother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_GreatGrandparent: Coding = {
  code: 'GGRPRN',
  display: 'great grandparent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Grandfather: Coding = {
  code: 'GRFTH',
  display: 'grandfather',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Grandmother: Coding = {
  code: 'GRMTH',
  display: 'grandmother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Grandchild: Coding = {
  code: 'GRNDCHILD',
  display: 'grandchild',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Granddaughter: Coding = {
  code: 'GRNDDAU',
  display: 'granddaughter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Grandson: Coding = {
  code: 'GRNDSON',
  display: 'grandson',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Grandparent: Coding = {
  code: 'GRPRN',
  display: 'grandparent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_HalfBrother: Coding = {
  code: 'HBRO',
  display: 'half-brother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_HalfSibling: Coding = {
  code: 'HSIB',
  display: 'half-sibling',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_HalfSister: Coding = {
  code: 'HSIS',
  display: 'half-sister',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Husband: Coding = {
  code: 'HUSB',
  display: 'husband',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v2_0131_InsuranceCompany: Coding = {
  code: 'I',
  display: 'Insurance Company',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0131',
};
const v3_RoleCode_Inlaw: Coding = {
  code: 'INLAW',
  display: 'inlaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_IdenticalTwinBrother: Coding = {
  code: 'ITWINBRO',
  display: 'identical twin brother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_IdenticalTwinSister: Coding = {
  code: 'ITWINSIS',
  display: 'identical twin sister',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MaternalAunt: Coding = {
  code: 'MAUNT',
  display: 'maternal aunt',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MaternalCousin: Coding = {
  code: 'MCOUSN',
  display: 'maternal cousin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MaternalGreatGrandfather: Coding = {
  code: 'MGGRFTH',
  display: 'maternal great-grandfather',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MaternalGreatGrandmother: Coding = {
  code: 'MGGRMTH',
  display: 'maternal great-grandmother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MaternalGreatGrandparent: Coding = {
  code: 'MGGRPRN',
  display: 'maternal great-grandparent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MaternalGrandfather: Coding = {
  code: 'MGRFTH',
  display: 'maternal grandfather',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MaternalGrandmother: Coding = {
  code: 'MGRMTH',
  display: 'maternal grandmother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MaternalGrandparent: Coding = {
  code: 'MGRPRN',
  display: 'maternal grandparent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MotherInLaw: Coding = {
  code: 'MTHINLAW',
  display: 'mother-in-law',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MaternalUncle: Coding = {
  code: 'MUNCLE',
  display: 'maternal uncle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v2_0131_NextOfKin: Coding = {
  code: 'N',
  display: 'Next-of-Kin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0131',
};
const v3_RoleCode_Neighbor: Coding = {
  code: 'NBOR',
  display: 'neighbor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NaturalBrother: Coding = {
  code: 'NBRO',
  display: 'natural brother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NaturalChild: Coding = {
  code: 'NCHILD',
  display: 'natural child',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Nephew: Coding = {
  code: 'NEPHEW',
  display: 'nephew',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Niece: Coding = {
  code: 'NIECE',
  display: 'niece',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NieceNephew: Coding = {
  code: 'NIENEPH',
  display: 'niece/nephew',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NaturalSibling: Coding = {
  code: 'NSIB',
  display: 'natural sibling',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NaturalSister: Coding = {
  code: 'NSIS',
  display: 'natural sister',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v2_0131_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0131',
};
const v3_RoleCode_Self: Coding = {
  code: 'ONESELF',
  display: 'self',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PaternalAunt: Coding = {
  code: 'PAUNT',
  display: 'paternal aunt',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PaternalCousin: Coding = {
  code: 'PCOUSN',
  display: 'paternal cousin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PaternalGreatGrandfather: Coding = {
  code: 'PGGRFTH',
  display: 'paternal great-grandfather',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PaternalGreatGrandmother: Coding = {
  code: 'PGGRMTH',
  display: 'paternal great-grandmother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PaternalGreatGrandparent: Coding = {
  code: 'PGGRPRN',
  display: 'paternal great-grandparent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PaternalGrandfather: Coding = {
  code: 'PGRFTH',
  display: 'paternal grandfather',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PaternalGrandmother: Coding = {
  code: 'PGRMTH',
  display: 'paternal grandmother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PaternalGrandparent: Coding = {
  code: 'PGRPRN',
  display: 'paternal grandparent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ParentInLaw: Coding = {
  code: 'PRNINLAW',
  display: 'parent in-law',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PaternalUncle: Coding = {
  code: 'PUNCLE',
  display: 'paternal uncle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Roommate: Coding = {
  code: 'ROOM',
  display: 'Roommate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v2_0131_StateAgency: Coding = {
  code: 'S',
  display: 'State Agency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0131',
};
const v3_RoleCode_Sibling: Coding = {
  code: 'SIB',
  display: 'sibling',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SiblingInLaw: Coding = {
  code: 'SIBINLAW',
  display: 'sibling in-law',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SignificantOther: Coding = {
  code: 'SIGOTHR',
  display: 'significant other',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Sister: Coding = {
  code: 'SIS',
  display: 'sister',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SisterInLaw: Coding = {
  code: 'SISINLAW',
  display: 'sister-in-law',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NaturalSon: Coding = {
  code: 'SON',
  display: 'natural son',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AdoptedSon: Coding = {
  code: 'SONADOPT',
  display: 'adopted son',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Son: Coding = {
  code: 'SONC',
  display: 'son',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FosterSon: Coding = {
  code: 'SONFOST',
  display: 'foster son',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SonInLaw: Coding = {
  code: 'SONINLAW',
  display: 'son in-law',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Stepbrother: Coding = {
  code: 'STPBRO',
  display: 'stepbrother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_StepChild: Coding = {
  code: 'STPCHLD',
  display: 'step child',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Stepdaughter: Coding = {
  code: 'STPDAU',
  display: 'stepdaughter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_StepSibling: Coding = {
  code: 'STPSIB',
  display: 'step sibling',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Stepsister: Coding = {
  code: 'STPSIS',
  display: 'stepsister',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Stepson: Coding = {
  code: 'STPSON',
  display: 'stepson',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_TwinBrother: Coding = {
  code: 'TWINBRO',
  display: 'twin brother',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_TwinSister: Coding = {
  code: 'TWINSIS',
  display: 'twin sister',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v2_0131_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0131',
};
const v3_RoleCode_Uncle: Coding = {
  code: 'UNCLE',
  display: 'uncle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Wife: Coding = {
  code: 'WIFE',
  display: 'wife',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
/**
 * A set of codes that can be used to indicate the relationship between a Patient and a Related Person.
 */
export const RelatedpersonRelationshiptype = {
  /**
   * PersonalRelationshipRoleType
   */
  PersonalRelationshipRoleType_v3_RoleCode: v3_RoleCode_PersonalRelationshipRoleType,
  /**
   * The player of the role (father) is a male who has taken the scoper (child) into their family through legal means and raises them as his own child.
   */
  AdoptiveFather_v3_RoleCode: v3_RoleCode_AdoptiveFather,
  /**
   * The player of the role (father) is a female who has taken the scoper (child) into their family through legal means and raises them as her own child.
   */
  AdoptiveMother_v3_RoleCode: v3_RoleCode_AdoptiveMother,
  /**
   * The player of the role (parent) has taken the scoper (child) into their family through legal means and raises them as his or her own child.
   */
  AdoptiveParent_v3_RoleCode: v3_RoleCode_AdoptiveParent,
  /**
   * The player of the role is a sister of the scoping person's mother or father.
   */
  Aunt_v3_RoleCode: v3_RoleCode_Aunt,
  /**
   * The player of the role is a male sharing one or both parents in common with the scoping entity.
   */
  Brother_v3_RoleCode: v3_RoleCode_Brother,
  /**
   * The player of the role is: (1) a brother of the scoping person's spouse, or (2) the husband of the scoping person's sister, or (3) the husband of a sister of the scoping person's spouse.
   */
  BrotherInLaw_v3_RoleCode: v3_RoleCode_BrotherInLaw,
  EmergencyContact_v2_0131: v2_0131_EmergencyContact,
  /**
   * The player of the role is a child of the scoping entity.
   */
  Child_v3_RoleCode: v3_RoleCode_Child,
  /**
   * The player of the role is a child taken into a family through legal means and raised by the scoping person (parent) as his or her own child.
   */
  AdoptedChild_v3_RoleCode: v3_RoleCode_AdoptedChild,
  /**
   * The player of the role is a child receiving parental care and nurture from the scoping person (parent) but not related to him or her through legal or blood ties.
   */
  FosterChild_v3_RoleCode: v3_RoleCode_FosterChild,
  /**
   * The player of the role is the spouse of scoping person's child.
   */
  ChildInLaw_v3_RoleCode: v3_RoleCode_ChildInLaw,
  /**
   * The player of the role is a relative of the scoping person descended from a common ancestor, such as a 	grandparent, by two or more steps in a diverging line.
   */
  Cousin_v3_RoleCode: v3_RoleCode_Cousin,
  /**
   * The player of the role is a female offspring of the scoping entity (parent).
   */
  NaturalDaughter_v3_RoleCode: v3_RoleCode_NaturalDaughter,
  /**
   * The player of the role is a female child taken into a family through legal means and raised by the scoping person (parent) as his or her own child.
   */
  AdoptedDaughter_v3_RoleCode: v3_RoleCode_AdoptedDaughter,
  /**
   * Description: The player of the role is a female child (of any type) of scoping entity (parent)
   */
  Daughter_v3_RoleCode: v3_RoleCode_Daughter,
  /**
   * The player of the role is a female child receiving parental care and nurture from the scoping person (parent) but not related to him or her through legal or blood ties.
   */
  FosterDaughter_v3_RoleCode: v3_RoleCode_FosterDaughter,
  /**
   * The player of the role is the wife of scoping person's son.
   */
  DaughterInLaw_v3_RoleCode: v3_RoleCode_DaughterInLaw,
  /**
   * The player of the role cohabits with the scoping person but is not the scoping person's spouse.
   */
  DomesticPartner_v3_RoleCode: v3_RoleCode_DomesticPartner,
  Employer_v2_0131: v2_0131_Employer,
  /**
   * Description: A family member not having an immediate genetic or legal relationship e.g. Aunt, cousin, great grandparent, grandchild, grandparent, niece, nephew or uncle.
   */
  ExtendedFamilyMember_v3_RoleCode: v3_RoleCode_ExtendedFamilyMember,
  FederalAgency_v2_0131: v2_0131_FederalAgency,
  /**
   * A relationship between two people characterizing their "familial" relationship
   */
  FamilyMember_v3_RoleCode: v3_RoleCode_FamilyMember,
  /**
   * Player of the role was previously joined to the scoping person in marriage and this marriage is now dissolved and inactive.
   *
   *
   *                            Usage Note: This is significant to indicate as some jurisdictions have different legal requirements for former spouse to access the patient's record, from a general friend.
   */
  FormerSpouse_v3_RoleCode: v3_RoleCode_FormerSpouse,
  /**
   * The player of the role is a person who is known, liked, and trusted by the scoping person.
   */
  UnrelatedFriend_v3_RoleCode: v3_RoleCode_UnrelatedFriend,
  /**
   * The player of the role is a male who begets or raises or nurtures the scoping entity (child).
   */
  Father_v3_RoleCode: v3_RoleCode_Father,
  /**
   * The player of the role (parent) who is a male state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterFather_v3_RoleCode: v3_RoleCode_FosterFather,
  /**
   * The player of the role is the father of the scoping person's husband or wife.
   */
  FatherInLaw_v3_RoleCode: v3_RoleCode_FatherInLaw,
  /**
   * The scoper and player were carried in the same womb and share common biological parents but are the product of distinct egg/sperm pairs.
   */
  FraternalTwin_v3_RoleCode: v3_RoleCode_FraternalTwin,
  /**
   * The scoper was carried in the same womb as the male player and shares common biological parents but is the product of a distinct egg/sperm pair.
   */
  FraternalTwinBrother_v3_RoleCode: v3_RoleCode_FraternalTwinBrother,
  /**
   * The scoper was carried in the same womb as the female player and shares common biological parents but is the product of a distinct egg/sperm pair.
   */
  FraternalTwinSister_v3_RoleCode: v3_RoleCode_FraternalTwinSister,
  /**
   * The player is a female whose womb carries the fetus of the scoper.  Generally used when the gestational mother and natural mother are not the same.
   */
  GestationalMother_v3_RoleCode: v3_RoleCode_GestationalMother,
  /**
   * The player of the role is the father of the scoping person's grandparent.
   */
  GreatGrandfather_v3_RoleCode: v3_RoleCode_GreatGrandfather,
  /**
   * The player of the role is the mother of the scoping person's grandparent.
   */
  GreatGrandmother_v3_RoleCode: v3_RoleCode_GreatGrandmother,
  /**
   * The player of the role is a parent of the scoping person's grandparent.
   */
  GreatGrandparent_v3_RoleCode: v3_RoleCode_GreatGrandparent,
  /**
   * The player of the role is the father of the scoping person's mother or father.
   */
  Grandfather_v3_RoleCode: v3_RoleCode_Grandfather,
  /**
   * The player of the role is the mother of the scoping person's mother or father.
   */
  Grandmother_v3_RoleCode: v3_RoleCode_Grandmother,
  /**
   * The player of the role is a child of the scoping person's son or daughter.
   */
  Grandchild_v3_RoleCode: v3_RoleCode_Grandchild,
  /**
   * The player of the role is a daughter of the scoping person's son or daughter.
   */
  Granddaughter_v3_RoleCode: v3_RoleCode_Granddaughter,
  /**
   * The player of the role is a son of the scoping person's son or daughter.
   */
  Grandson_v3_RoleCode: v3_RoleCode_Grandson,
  /**
   * The player of the role is a parent of the scoping person's mother or father.
   */
  Grandparent_v3_RoleCode: v3_RoleCode_Grandparent,
  /**
   * The player of the role is a male related to the scoping entity by sharing only one biological parent.
   */
  HalfBrother_v3_RoleCode: v3_RoleCode_HalfBrother,
  /**
   * The player of the role is related to the scoping entity by sharing only one biological parent.
   */
  HalfSibling_v3_RoleCode: v3_RoleCode_HalfSibling,
  /**
   * The player of the role is a female related to the scoping entity by sharing only one biological parent.
   */
  HalfSister_v3_RoleCode: v3_RoleCode_HalfSister,
  /**
   * The player of the role is a man joined to a woman (scoping person) in marriage.
   */
  Husband_v3_RoleCode: v3_RoleCode_Husband,
  InsuranceCompany_v2_0131: v2_0131_InsuranceCompany,
  /**
   * A relationship between an individual and a member of their spousal partner's immediate family.
   */
  Inlaw_v3_RoleCode: v3_RoleCode_Inlaw,
  /**
   * The scoper and player are offspring of the same egg-sperm pair.
   */
  IdenticalTwin_v3_RoleCode: v3_RoleCode_IdenticalTwin,
  /**
   * The male scoper is an offspring of the same egg-sperm pair as the male player.
   */
  IdenticalTwinBrother_v3_RoleCode: v3_RoleCode_IdenticalTwinBrother,
  /**
   * The female scoper is an offspring of the same egg-sperm pair as the female player.
   */
  IdenticalTwinSister_v3_RoleCode: v3_RoleCode_IdenticalTwinSister,
  /**
   * Description:The player of the role is a biological sister of the scoping person's biological mother.
   */
  MaternalAunt_v3_RoleCode: v3_RoleCode_MaternalAunt,
  /**
   * Description:The player of the role is a biological relative of the scoping person descended from a common ancestor on the player's mother's side, such as a grandparent, by two or more steps in a diverging line.
   */
  MaternalCousin_v3_RoleCode: v3_RoleCode_MaternalCousin,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological mother's parent.
   */
  MaternalGreatGrandfather_v3_RoleCode: v3_RoleCode_MaternalGreatGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological mother's parent.
   */
  MaternalGreatGrandmother_v3_RoleCode: v3_RoleCode_MaternalGreatGrandmother,
  /**
   * Description:The player of the role is a biological parent of the scoping person's biological mother's parent.
   */
  MaternalGreatGrandparent_v3_RoleCode: v3_RoleCode_MaternalGreatGrandparent,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological mother.
   */
  MaternalGrandfather_v3_RoleCode: v3_RoleCode_MaternalGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological mother.
   */
  MaternalGrandmother_v3_RoleCode: v3_RoleCode_MaternalGrandmother,
  /**
   * Description:The player of the role is the biological parent of the scoping person's biological mother.
   */
  MaternalGrandparent_v3_RoleCode: v3_RoleCode_MaternalGrandparent,
  /**
   * The player of the role is a female who conceives, gives birth to, or raises and nurtures the scoping entity (child).
   */
  Mother_v3_RoleCode: v3_RoleCode_Mother,
  /**
   * The player of the role (parent) who is a female state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterMother_v3_RoleCode: v3_RoleCode_FosterMother,
  /**
   * The player of the role is the mother of the scoping person's husband or wife.
   */
  MotherInLaw_v3_RoleCode: v3_RoleCode_MotherInLaw,
  /**
   * Description:The player of the role is a biological brother of the scoping person's biological mother.
   */
  MaternalUncle_v3_RoleCode: v3_RoleCode_MaternalUncle,
  NextOfKin_v2_0131: v2_0131_NextOfKin,
  /**
   * The player of the role lives near or next to the 	scoping person.
   */
  Neighbor_v3_RoleCode: v3_RoleCode_Neighbor,
  /**
   * The player of the role is a male having the same biological parents as the scoping entity.
   */
  NaturalBrother_v3_RoleCode: v3_RoleCode_NaturalBrother,
  /**
   * The player of the role is an offspring of the scoping entity as determined by birth.
   */
  NaturalChild_v3_RoleCode: v3_RoleCode_NaturalChild,
  /**
   * The player of the role is a son of the scoping person's brother or sister or of the brother or sister of the 	scoping person's spouse.
   */
  Nephew_v3_RoleCode: v3_RoleCode_Nephew,
  /**
   * The player of the role is a male who begets the scoping entity (child).
   */
  NaturalFather_v3_RoleCode: v3_RoleCode_NaturalFather,
  /**
   * Indicates the biologic male parent of a fetus.
   */
  NaturalFatherOfFetus_v3_RoleCode: v3_RoleCode_NaturalFatherOfFetus,
  /**
   * The player of the role is a daughter of the scoping person's brother or sister or of the brother or sister of the 	scoping person's spouse.
   */
  Niece_v3_RoleCode: v3_RoleCode_Niece,
  /**
   * The player of the role is a child of scoping person's brother or sister or of the brother or sister of the 	scoping person's spouse.
   */
  NieceNephew_v3_RoleCode: v3_RoleCode_NieceNephew,
  /**
   * The player of the role is a female who conceives or gives birth to the scoping entity (child).
   */
  NaturalMother_v3_RoleCode: v3_RoleCode_NaturalMother,
  /**
   * The player is the biologic female parent of the scoping fetus.
   */
  NaturalMotherOfFetus_v3_RoleCode: v3_RoleCode_NaturalMotherOfFetus,
  /**
   * natural parent
   */
  NaturalParent_v3_RoleCode: v3_RoleCode_NaturalParent,
  /**
   * The player of the role has both biological parents in common with the scoping entity.
   */
  NaturalSibling_v3_RoleCode: v3_RoleCode_NaturalSibling,
  /**
   * The player of the role is a female having the same biological parents as the scoping entity.
   */
  NaturalSister_v3_RoleCode: v3_RoleCode_NaturalSister,
  Other_v2_0131: v2_0131_Other,
  /**
   * The relationship that a person has with his or her self.
   */
  Self_v3_RoleCode: v3_RoleCode_Self,
  /**
   * Description:The player of the role is a biological sister of the scoping person's biological father.
   */
  PaternalAunt_v3_RoleCode: v3_RoleCode_PaternalAunt,
  /**
   * Description:The player of the role is a biological relative of the scoping person descended from a common ancestor on the player's father's side, such as a grandparent, by two or more steps in a diverging line.
   */
  PaternalCousin_v3_RoleCode: v3_RoleCode_PaternalCousin,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological father's parent.
   */
  PaternalGreatGrandfather_v3_RoleCode: v3_RoleCode_PaternalGreatGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological father's parent.
   */
  PaternalGreatGrandmother_v3_RoleCode: v3_RoleCode_PaternalGreatGrandmother,
  /**
   * Description:The player of the role is a biological parent of the scoping person's biological father's parent.
   */
  PaternalGreatGrandparent_v3_RoleCode: v3_RoleCode_PaternalGreatGrandparent,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological father.
   */
  PaternalGrandfather_v3_RoleCode: v3_RoleCode_PaternalGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological father.
   */
  PaternalGrandmother_v3_RoleCode: v3_RoleCode_PaternalGrandmother,
  /**
   * Description:The player of the role is the biological parent of the scoping person's biological father.
   */
  PaternalGrandparent_v3_RoleCode: v3_RoleCode_PaternalGrandparent,
  /**
   * The player of the role is one who begets, gives birth to, or nurtures and raises the scoping entity (child).
   */
  Parent_v3_RoleCode: v3_RoleCode_Parent,
  /**
   * The player of the role (parent) who is a state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterParent_v3_RoleCode: v3_RoleCode_FosterParent,
  /**
   * The player of the role is the parent of scoping person's husband or wife.
   */
  ParentInLaw_v3_RoleCode: v3_RoleCode_ParentInLaw,
  /**
   * Description:The player of the role is a biological brother of the scoping person's biological father.
   */
  PaternalUncle_v3_RoleCode: v3_RoleCode_PaternalUncle,
  /**
   * One who shares living quarters with the subject.
   */
  Roommate_v3_RoleCode: v3_RoleCode_Roommate,
  StateAgency_v2_0131: v2_0131_StateAgency,
  /**
   * The player of the role shares one or both parents in common with the scoping entity.
   */
  Sibling_v3_RoleCode: v3_RoleCode_Sibling,
  /**
   * The player of the role is: (1) a sibling of the scoping person's spouse, or (2) the spouse of the scoping person's sibling, or (3) the spouse of a sibling of the scoping person's spouse.
   */
  SiblingInLaw_v3_RoleCode: v3_RoleCode_SiblingInLaw,
  /**
   * A person who is important to one's well being; especially a spouse or one in a similar relationship.  (The player is the one who is important)
   */
  SignificantOther_v3_RoleCode: v3_RoleCode_SignificantOther,
  /**
   * The player of the role is a female sharing one or both parents in common with the scoping entity.
   */
  Sister_v3_RoleCode: v3_RoleCode_Sister,
  /**
   * The player of the role is: (1) a sister of the scoping person's spouse, or (2) the wife of the scoping person's brother, or (3) the wife of a brother of the scoping person's spouse.
   */
  SisterInLaw_v3_RoleCode: v3_RoleCode_SisterInLaw,
  /**
   * The player of the role is a male offspring of the scoping entity (parent).
   */
  NaturalSon_v3_RoleCode: v3_RoleCode_NaturalSon,
  /**
   * The player of the role is a male child taken into a family through legal means and raised by the scoping person (parent) as his or her own child.
   */
  AdoptedSon_v3_RoleCode: v3_RoleCode_AdoptedSon,
  /**
   * Description: The player of the role is a male child (of any type) of scoping entity (parent)
   */
  Son_v3_RoleCode: v3_RoleCode_Son,
  /**
   * The player of the role is a male child receiving parental care and nurture from the scoping person (parent) but not related to him or her through legal or blood ties.
   */
  FosterSon_v3_RoleCode: v3_RoleCode_FosterSon,
  /**
   * The player of the role is the husband of scoping person's daughter.
   */
  SonInLaw_v3_RoleCode: v3_RoleCode_SonInLaw,
  /**
   * The player of the role is a marriage partner of the scoping person.
   */
  Spouse_v3_RoleCode: v3_RoleCode_Spouse,
  /**
   * The player of the role is a son of the scoping person's stepparent.
   */
  Stepbrother_v3_RoleCode: v3_RoleCode_Stepbrother,
  /**
   * The player of the role is a child of the scoping person's spouse by a previous union.
   */
  StepChild_v3_RoleCode: v3_RoleCode_StepChild,
  /**
   * The player of the role is a daughter of the scoping person's spouse by a previous union.
   */
  Stepdaughter_v3_RoleCode: v3_RoleCode_Stepdaughter,
  /**
   * The player of the role is the husband of scoping person's mother and not the scoping person's natural father.
   */
  Stepfather_v3_RoleCode: v3_RoleCode_Stepfather,
  /**
   * The player of the role is the wife of scoping person's father and not the scoping person's natural mother.
   */
  Stepmother_v3_RoleCode: v3_RoleCode_Stepmother,
  /**
   * The player of the role is the spouse of the scoping person's parent and not the scoping person's natural parent.
   */
  StepParent_v3_RoleCode: v3_RoleCode_StepParent,
  /**
   * The player of the role is a child of the scoping person's stepparent.
   */
  StepSibling_v3_RoleCode: v3_RoleCode_StepSibling,
  /**
   * The player of the role is a daughter of the scoping person's stepparent.
   */
  Stepsister_v3_RoleCode: v3_RoleCode_Stepsister,
  /**
   * The player of the role is a son of the scoping person's spouse by a previous union.
   */
  Stepson_v3_RoleCode: v3_RoleCode_Stepson,
  /**
   * The scoper and player were carried in the same womb and shared common biological parents.
   */
  Twin_v3_RoleCode: v3_RoleCode_Twin,
  /**
   * The scoper was carried in the same womb as the male player and shares common biological parents.
   */
  TwinBrother_v3_RoleCode: v3_RoleCode_TwinBrother,
  /**
   * The scoper was carried in the same womb as the female player and shares common biological parents.
   */
  TwinSister_v3_RoleCode: v3_RoleCode_TwinSister,
  Unknown_v2_0131: v2_0131_Unknown,
  /**
   * The player of the role is a brother of the scoping person's mother or father.
   */
  Uncle_v3_RoleCode: v3_RoleCode_Uncle,
  /**
   * The player of the role is a woman joined to a man (scoping person) in marriage.
   */
  Wife_v3_RoleCode: v3_RoleCode_Wife,
};
const relationship_Self: Coding = {
  code: '1',
  display: 'Self',
  system: 'http://hl7.org/fhir/relationship',
};
const relationship_Spouse: Coding = {
  code: '2',
  display: 'Spouse',
  system: 'http://hl7.org/fhir/relationship',
};
const relationship_Child: Coding = {
  code: '3',
  display: 'Child',
  system: 'http://hl7.org/fhir/relationship',
};
const relationship_CommonLawSpouse: Coding = {
  code: '4',
  display: 'Common Law Spouse',
  system: 'http://hl7.org/fhir/relationship',
};
const relationship_Other: Coding = {
  code: '5',
  display: 'Other',
  system: 'http://hl7.org/fhir/relationship',
};
/**
 * This value set includes the Patient to subscriber relationship codes.
 */
export const Relationship = {
  /**
   * The patient is the subscriber (policy holder)
   */
  Self: relationship_Self,
  /**
   * The patient is the spouse or equivalent of the subscriber (policy holder)
   */
  Spouse: relationship_Spouse,
  /**
   * The patient is the child of the subscriber (policy holder)
   */
  Child: relationship_Child,
  /**
   * The patient is the common law spouse of the subscriber (policy holder)
   */
  CommonLawSpouse: relationship_CommonLawSpouse,
  /**
   * The patient has some other relationship, such as parent, to the subscriber (policy holder)
   */
  Other: relationship_Other,
};
const relation_type_ReplacedBy: Coding = {
  code: 'is-replaced-by',
  display: 'Replaced By',
  system: 'http://hl7.org/fhir/relation-type',
};
const relation_type_Triggers: Coding = {
  code: 'triggers',
  display: 'Triggers',
  system: 'http://hl7.org/fhir/relation-type',
};
/**
 * The type of relations between entries.
 */
export const RelationType = {
  /**
   * the related entry represents an item that replaces the current retired item.
   */
  ReplacedBy: relation_type_ReplacedBy,
  /**
   * the related entry represents an activity that may be triggered by the current item.
   */
  Triggers: relation_type_Triggers,
};
const remittance_outcome_ProcessingComplete: Coding = {
  code: 'complete',
  display: 'Processing Complete',
  system: 'http://hl7.org/fhir/remittance-outcome',
};
const remittance_outcome_Error: Coding = {
  code: 'error',
  display: 'Error',
  system: 'http://hl7.org/fhir/remittance-outcome',
};
const remittance_outcome_PartialProcessing: Coding = {
  code: 'partial',
  display: 'Partial Processing',
  system: 'http://hl7.org/fhir/remittance-outcome',
};
const remittance_outcome_Queued: Coding = {
  code: 'queued',
  display: 'Queued',
  system: 'http://hl7.org/fhir/remittance-outcome',
};
/**
 * This value set includes Claim Processing Outcome codes.
 */
export const RemittanceOutcome = {
  /**
   * The processing has completed without errors
   */
  ProcessingComplete: remittance_outcome_ProcessingComplete,
  /**
   * One or more errors have been detected in the Claim
   */
  Error: remittance_outcome_Error,
  /**
   * No errors have been detected in the Claim and some of the adjudication has been performed.
   */
  PartialProcessing: remittance_outcome_PartialProcessing,
  /**
   * The Claim/Pre-authorization/Pre-determination has been received but processing has not begun.
   */
  Queued: remittance_outcome_Queued,
};
const report_action_result_codes_Error: Coding = {
  code: 'error',
  display: 'Error',
  system: 'http://hl7.org/fhir/report-action-result-codes',
};
const report_action_result_codes_Fail: Coding = {
  code: 'fail',
  display: 'Fail',
  system: 'http://hl7.org/fhir/report-action-result-codes',
};
const report_action_result_codes_Pass: Coding = {
  code: 'pass',
  display: 'Pass',
  system: 'http://hl7.org/fhir/report-action-result-codes',
};
const report_action_result_codes_Skip: Coding = {
  code: 'skip',
  display: 'Skip',
  system: 'http://hl7.org/fhir/report-action-result-codes',
};
const report_action_result_codes_Warning: Coding = {
  code: 'warning',
  display: 'Warning',
  system: 'http://hl7.org/fhir/report-action-result-codes',
};
/**
 * The results of executing an action.
 */
export const ReportActionResultCodes = {
  /**
   * The action encountered a fatal error and the engine was unable to process.
   */
  Error: report_action_result_codes_Error,
  /**
   * The action failed.
   */
  Fail: report_action_result_codes_Fail,
  /**
   * The action was successful.
   */
  Pass: report_action_result_codes_Pass,
  /**
   * The action was skipped.
   */
  Skip: report_action_result_codes_Skip,
  /**
   * The action passed but with warnings.
   */
  Warning: report_action_result_codes_Warning,
};
const report_participant_type_Client: Coding = {
  code: 'client',
  display: 'Client',
  system: 'http://hl7.org/fhir/report-participant-type',
};
const report_participant_type_Server: Coding = {
  code: 'server',
  display: 'Server',
  system: 'http://hl7.org/fhir/report-participant-type',
};
const report_participant_type_TestEngine: Coding = {
  code: 'test-engine',
  display: 'Test Engine',
  system: 'http://hl7.org/fhir/report-participant-type',
};
/**
 * The type of participant.
 */
export const ReportParticipantType = {
  /**
   * A FHIR Client.
   */
  Client: report_participant_type_Client,
  /**
   * A FHIR Server.
   */
  Server: report_participant_type_Server,
  /**
   * The test execution engine.
   */
  TestEngine: report_participant_type_TestEngine,
};
const report_result_codes_Fail: Coding = {
  code: 'fail',
  display: 'Fail',
  system: 'http://hl7.org/fhir/report-result-codes',
};
const report_result_codes_Pass: Coding = {
  code: 'pass',
  display: 'Pass',
  system: 'http://hl7.org/fhir/report-result-codes',
};
const report_result_codes_Pending: Coding = {
  code: 'pending',
  display: 'Pending',
  system: 'http://hl7.org/fhir/report-result-codes',
};
/**
 * The reported execution result.
 */
export const ReportResultCodes = {
  /**
   * One or more test operations failed one or more asserts.
   */
  Fail: report_result_codes_Fail,
  /**
   * All test operations successfully passed all asserts.
   */
  Pass: report_result_codes_Pass,
  /**
   * One or more test operations is pending execution completion.
   */
  Pending: report_result_codes_Pending,
};
const report_status_codes_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/report-status-codes',
};
const report_status_codes_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered In Error',
  system: 'http://hl7.org/fhir/report-status-codes',
};
const report_status_codes_InProgress: Coding = {
  code: 'in-progress',
  display: 'In Progress',
  system: 'http://hl7.org/fhir/report-status-codes',
};
const report_status_codes_Stopped: Coding = {
  code: 'stopped',
  display: 'Stopped',
  system: 'http://hl7.org/fhir/report-status-codes',
};
const report_status_codes_Waiting: Coding = {
  code: 'waiting',
  display: 'Waiting',
  system: 'http://hl7.org/fhir/report-status-codes',
};
/**
 * The current status of the test report.
 */
export const ReportStatusCodes = {
  /**
   * All test operations have completed.
   */
  Completed: report_status_codes_Completed,
  /**
   * This test report was entered or created in error.
   */
  EnteredInError: report_status_codes_EnteredInError,
  /**
   * A test operations is currently executing.
   */
  InProgress: report_status_codes_InProgress,
  /**
   * The test script execution was manually stopped.
   */
  Stopped: report_status_codes_Stopped,
  /**
   * A test operation is waiting for an external client request.
   */
  Waiting: report_status_codes_Waiting,
};
const repository_type_ClickAndSee: Coding = {
  code: 'directlink',
  display: 'Click and see',
  system: 'http://hl7.org/fhir/repository-type',
};
const repository_type_ResultCannotBeAccessUnlessAnAccountIsLoggedIn: Coding = {
  code: 'login',
  display: 'Result cannot be access unless an account is logged in',
  system: 'http://hl7.org/fhir/repository-type',
};
const repository_type_ResultNeedToBeFetchedWithAPIAndNeedLOGINOrCookiesAreRequiredWhenVisitingTheLinkOfResource: Coding = {
  code: 'oauth',
  display: 'Result need to be fetched with API and need LOGIN( or cookies are required when visiting the link of resource)',
  system: 'http://hl7.org/fhir/repository-type',
};
const repository_type_TheURLIsTheRESTfulOrOtherKindOfAPIThatCanAccessToTheResult: Coding = {
  code: 'openapi',
  display: 'The URL is the RESTful or other kind of API that can access to the result.',
  system: 'http://hl7.org/fhir/repository-type',
};
const repository_type_SomeOtherComplicatedOrParticularWayToGetResourceFromURL: Coding = {
  code: 'other',
  display: 'Some other complicated or particular way to get resource from URL.',
  system: 'http://hl7.org/fhir/repository-type',
};
/**
 * Type for access of external URI.
 */
export const RepositoryType = {
  /**
   * When URL is clicked, the resource can be seen directly (by webpage or by download link format).
   */
  ClickAndSee: repository_type_ClickAndSee,
  /**
   * When logged into the website, the resource can be seen.
   */
  ResultCannotBeAccessUnlessAnAccountIsLoggedIn: repository_type_ResultCannotBeAccessUnlessAnAccountIsLoggedIn,
  /**
   * When logged in and  follow the API in the website related with URL, the resource can be seen.
   */
  ResultNeedToBeFetchedWithAPIAndNeedLOGINOrCookiesAreRequiredWhenVisitingTheLinkOfResource: repository_type_ResultNeedToBeFetchedWithAPIAndNeedLOGINOrCookiesAreRequiredWhenVisitingTheLinkOfResource,
  /**
   * When the API method (e.g. [base_url]/[parameter]) related with the URL of the website is executed, the resource can be seen directly (usually in JSON or XML format).
   */
  TheURLIsTheRESTfulOrOtherKindOfAPIThatCanAccessToTheResult: repository_type_TheURLIsTheRESTfulOrOtherKindOfAPIThatCanAccessToTheResult,
  /**
   * Some other complicated or particular way to get resource from URL.
   */
  SomeOtherComplicatedOrParticularWayToGetResourceFromURL: repository_type_SomeOtherComplicatedOrParticularWayToGetResourceFromURL,
};
const request_intent_Directive: Coding = {
  code: 'directive',
  display: 'Directive',
  system: 'http://hl7.org/fhir/request-intent',
};
const request_intent_FillerOrder: Coding = {
  code: 'filler-order',
  display: 'Filler Order',
  system: 'http://hl7.org/fhir/request-intent',
};
const request_intent_InstanceOrder: Coding = {
  code: 'instance-order',
  display: 'Instance Order',
  system: 'http://hl7.org/fhir/request-intent',
};
const request_intent_OriginalOrder: Coding = {
  code: 'original-order',
  display: 'Original Order',
  system: 'http://hl7.org/fhir/request-intent',
};
const request_intent_ReflexOrder: Coding = {
  code: 'reflex-order',
  display: 'Reflex Order',
  system: 'http://hl7.org/fhir/request-intent',
};
/**
 * Codes indicating the degree of authority/intentionality associated with a request.
 */
export const RequestIntent = {
  /**
   * The request represents a legally binding instruction authored by a Patient or RelatedPerson.
   */
  Directive: request_intent_Directive,
  /**
   * The request represents the view of an authorization instantiated by a fulfilling system representing the details of the fulfiller's intention to act upon a submitted order.
   */
  FillerOrder: request_intent_FillerOrder,
  /**
   * An order created in fulfillment of a broader order that represents the authorization for a single activity occurrence.  E.g. The administration of a single dose of a drug.
   */
  InstanceOrder: request_intent_InstanceOrder,
  /**
   * The request represents a component or option for a RequestGroup that establishes timing, conditionality and/or other constraints among a set of requests.  Refer to [[[RequestGroup]]] for additional information on how this status is used.
   */
  Option: request_intent_Option,
  /**
   * The request represents a request/demand and authorization for action by a Practitioner.
   */
  Order: request_intent_Order,
  /**
   * The request represents an original authorization for action.
   */
  OriginalOrder: request_intent_OriginalOrder,
  /**
   * The request represents an intention to ensure something occurs without providing an authorization for others to act.
   */
  Plan: request_intent_Plan,
  /**
   * The request is a suggestion made by someone/something that does not have an intention to ensure it occurs and without providing an authorization to act.
   */
  Proposal: request_intent_Proposal,
  /**
   * The request represents an automatically generated supplemental authorization for action based on a parent authorization together with initial results of the action taken against that parent authorization.
   */
  ReflexOrder: request_intent_ReflexOrder,
};
const request_priority_ASAP: Coding = {
  code: 'asap',
  display: 'ASAP',
  system: 'http://hl7.org/fhir/request-priority',
};
const request_priority_Routine: Coding = {
  code: 'routine',
  display: 'Routine',
  system: 'http://hl7.org/fhir/request-priority',
};
const request_priority_STAT: Coding = {
  code: 'stat',
  display: 'STAT',
  system: 'http://hl7.org/fhir/request-priority',
};
const request_priority_Urgent: Coding = {
  code: 'urgent',
  display: 'Urgent',
  system: 'http://hl7.org/fhir/request-priority',
};
/**
 * The clinical priority of a diagnostic order.
 */
export const RequestPriority = {
  /**
   * The request should be actioned as soon as possible - higher priority than urgent.
   */
  ASAP: request_priority_ASAP,
  /**
   * The request has normal priority.
   */
  Routine: request_priority_Routine,
  /**
   * The request should be actioned immediately - highest possible priority.  E.g. an emergency.
   */
  STAT: request_priority_STAT,
  /**
   * The request should be actioned promptly - higher priority than routine.
   */
  Urgent: request_priority_Urgent,
};
const request_resource_types_Appointment: Coding = {
  code: 'Appointment',
  display: 'Appointment',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_AppointmentResponse: Coding = {
  code: 'AppointmentResponse',
  display: 'AppointmentResponse',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_CarePlan: Coding = {
  code: 'CarePlan',
  display: 'CarePlan',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_Claim: Coding = {
  code: 'Claim',
  display: 'Claim',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_CommunicationRequest: Coding = {
  code: 'CommunicationRequest',
  display: 'CommunicationRequest',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_Contract: Coding = {
  code: 'Contract',
  display: 'Contract',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_DeviceRequest: Coding = {
  code: 'DeviceRequest',
  display: 'DeviceRequest',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_EnrollmentRequest: Coding = {
  code: 'EnrollmentRequest',
  display: 'EnrollmentRequest',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_ImmunizationRecommendation: Coding = {
  code: 'ImmunizationRecommendation',
  display: 'ImmunizationRecommendation',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_MedicationRequest: Coding = {
  code: 'MedicationRequest',
  display: 'MedicationRequest',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_NutritionOrder: Coding = {
  code: 'NutritionOrder',
  display: 'NutritionOrder',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_ServiceRequest: Coding = {
  code: 'ServiceRequest',
  display: 'ServiceRequest',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_SupplyRequest: Coding = {
  code: 'SupplyRequest',
  display: 'SupplyRequest',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_Task: Coding = {
  code: 'Task',
  display: 'Task',
  system: 'http://hl7.org/fhir/request-resource-types',
};
const request_resource_types_VisionPrescription: Coding = {
  code: 'VisionPrescription',
  display: 'VisionPrescription',
  system: 'http://hl7.org/fhir/request-resource-types',
};
/**
 * A list of all the request resource types defined in this version of the FHIR specification.
 */
export const RequestResourceTypes = {
  /**
   * A booking of a healthcare event among patient(s), practitioner(s), related person(s) and/or device(s) for a specific date/time. This may result in one or more Encounter(s).
   */
  Appointment: request_resource_types_Appointment,
  /**
   * A reply to an appointment request for a patient and/or practitioner(s), such as a confirmation or rejection.
   */
  AppointmentResponse: request_resource_types_AppointmentResponse,
  /**
   * Healthcare plan for patient or group.
   */
  CarePlan: request_resource_types_CarePlan,
  /**
   * Claim, Pre-determination or Pre-authorization.
   */
  Claim: request_resource_types_Claim,
  /**
   * A request for information to be sent to a receiver.
   */
  CommunicationRequest: request_resource_types_CommunicationRequest,
  /**
   * Legal Agreement.
   */
  Contract: request_resource_types_Contract,
  /**
   * Medical device request.
   */
  DeviceRequest: request_resource_types_DeviceRequest,
  /**
   * Enrollment request.
   */
  EnrollmentRequest: request_resource_types_EnrollmentRequest,
  /**
   * Guidance or advice relating to an immunization.
   */
  ImmunizationRecommendation: request_resource_types_ImmunizationRecommendation,
  /**
   * Ordering of medication for patient or group.
   */
  MedicationRequest: request_resource_types_MedicationRequest,
  /**
   * Diet, formula or nutritional supplement request.
   */
  NutritionOrder: request_resource_types_NutritionOrder,
  /**
   * A record of a request for service such as diagnostic investigations, treatments, or operations to be performed.
   */
  ServiceRequest: request_resource_types_ServiceRequest,
  /**
   * Request for a medication, substance or device.
   */
  SupplyRequest: request_resource_types_SupplyRequest,
  /**
   * A task to be performed.
   */
  Task: request_resource_types_Task,
  /**
   * Prescription for vision correction products for a patient.
   */
  VisionPrescription: request_resource_types_VisionPrescription,
};
const request_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/request-status',
};
const request_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/request-status',
};
const request_status_Draft: Coding = {
  code: 'draft',
  display: 'Draft',
  system: 'http://hl7.org/fhir/request-status',
};
const request_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/request-status',
};
const request_status_OnHold: Coding = {
  code: 'on-hold',
  display: 'On Hold',
  system: 'http://hl7.org/fhir/request-status',
};
const request_status_Revoked: Coding = {
  code: 'revoked',
  display: 'Revoked',
  system: 'http://hl7.org/fhir/request-status',
};
const request_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/request-status',
};
/**
 * Codes identifying the lifecycle stage of a request.
 */
export const RequestStatus = {
  /**
   * The request is in force and ready to be acted upon.
   */
  Active: request_status_Active,
  /**
   * The activity described by the request has been fully performed.  No further activity will occur.
   */
  Completed: request_status_Completed,
  /**
   * The request has been created but is not yet complete or ready for action.
   */
  Draft: request_status_Draft,
  /**
   * This request should never have existed and should be considered 'void'.  (It is possible that real-world decisions were based on it.  If real-world activity has occurred, the status should be "revoked" rather than "entered-in-error".).
   */
  EnteredInError: request_status_EnteredInError,
  /**
   * The request (and any implicit authorization to act) has been temporarily withdrawn but is expected to resume in the future.
   */
  OnHold: request_status_OnHold,
  /**
   * The request (and any implicit authorization to act) has been terminated prior to the known full completion of the intended actions.  No further activity should occur.
   */
  Revoked: request_status_Revoked,
  /**
   * The authoring/source system does not know which of the status values currently applies for this request.  Note: This concept is not to be used for "other" - one of the listed statuses is presumed to apply,  but the authoring/source system does not know which.
   */
  Unknown: request_status_Unknown,
};
const research_element_type_Exposure: Coding = {
  code: 'exposure',
  display: 'Exposure',
  system: 'http://hl7.org/fhir/research-element-type',
};
const research_element_type_Outcome: Coding = {
  code: 'outcome',
  display: 'Outcome',
  system: 'http://hl7.org/fhir/research-element-type',
};
const research_element_type_Population: Coding = {
  code: 'population',
  display: 'Population',
  system: 'http://hl7.org/fhir/research-element-type',
};
/**
 * The possible types of research elements (E.g. Population, Exposure, Outcome).
 */
export const ResearchElementType = {
  /**
   * The element defines an exposure within the population that is being researched.
   */
  Exposure: research_element_type_Exposure,
  /**
   * The element defines an outcome within the population that is being researched.
   */
  Outcome: research_element_type_Outcome,
  /**
   * The element defines the population that forms the basis for research.
   */
  Population: research_element_type_Population,
};
const research_study_objective_type_Exploratory: Coding = {
  code: 'exploratory',
  display: 'Exploratory',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-objective-type',
};
const research_study_objective_type_Primary: Coding = {
  code: 'primary',
  display: 'Primary',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-objective-type',
};
const research_study_objective_type_Secondary: Coding = {
  code: 'secondary',
  display: 'Secondary',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-objective-type',
};
/**
 * Codes for the kind of study objective.
 */
export const ResearchStudyObjectiveType = {
  /**
   * Exploratory questions to be answered in the study.
   */
  Exploratory: research_study_objective_type_Exploratory,
  /**
   * The main question to be answered, and the one that drives any statistical planning for the study—e.g., calculation of the sample size to provide the appropriate power for statistical testing.
   */
  Primary: research_study_objective_type_Primary,
  /**
   * Question to be answered in the study that is of lesser importance than the primary objective.
   */
  Secondary: research_study_objective_type_Secondary,
};
const research_study_phase_EarlyPhase1: Coding = {
  code: 'early-phase-1',
  display: 'Early Phase 1',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-phase',
};
const research_study_phase_NA: Coding = {
  code: 'n-a',
  display: 'N/A',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-phase',
};
const research_study_phase_Phase1: Coding = {
  code: 'phase-1',
  display: 'Phase 1',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-phase',
};
const research_study_phase_Phase1Phase2: Coding = {
  code: 'phase-1-phase-2',
  display: 'Phase 1/Phase 2',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-phase',
};
const research_study_phase_Phase2: Coding = {
  code: 'phase-2',
  display: 'Phase 2',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-phase',
};
const research_study_phase_Phase2Phase3: Coding = {
  code: 'phase-2-phase-3',
  display: 'Phase 2/Phase 3',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-phase',
};
const research_study_phase_Phase3: Coding = {
  code: 'phase-3',
  display: 'Phase 3',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-phase',
};
const research_study_phase_Phase4: Coding = {
  code: 'phase-4',
  display: 'Phase 4',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-phase',
};
/**
 * Codes for the stage in the progression of a therapy from initial experimental use in humans in clinical trials to post-market evaluation.
 */
export const ResearchStudyPhase = {
  /**
   * Designation for optional exploratory trials conducted in accordance with the United States Food and Drug Administration's (FDA) 2006 Guidance on Exploratory Investigational New Drug (IND) Studies. Formerly called Phase 0.
   */
  EarlyPhase1: research_study_phase_EarlyPhase1,
  /**
   * Trials without phases (for example, studies of devices or behavioral interventions).
   */
  NA: research_study_phase_NA,
  /**
   * Includes initial studies to determine the metabolism and pharmacologic actions of drugs in humans, the side effects associated with increasing doses, and to gain early evidence of effectiveness; may include healthy participants and/or patients.
   */
  Phase1: research_study_phase_Phase1,
  /**
   * Trials that are a combination of phases 1 and 2.
   */
  Phase1Phase2: research_study_phase_Phase1Phase2,
  /**
   * Includes controlled clinical studies conducted to evaluate the effectiveness of the drug for a particular indication or indications in participants with the disease or condition under study and to determine the common short-term side effects and risks.
   */
  Phase2: research_study_phase_Phase2,
  /**
   * Trials that are a combination of phases 2 and 3.
   */
  Phase2Phase3: research_study_phase_Phase2Phase3,
  /**
   * Includes trials conducted after preliminary evidence suggesting effectiveness of the drug has been obtained, and are intended to gather additional information to evaluate the overall benefit-risk relationship of the drug.
   */
  Phase3: research_study_phase_Phase3,
  /**
   * Studies of FDA-approved drugs to delineate additional information including the drug's risks, benefits, and optimal use.
   */
  Phase4: research_study_phase_Phase4,
};
const research_study_prim_purp_type_BasicScience: Coding = {
  code: 'basic-science',
  display: 'Basic Science',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-prim-purp-type',
};
const research_study_prim_purp_type_DeviceFeasibility: Coding = {
  code: 'device-feasibility',
  display: 'Device Feasibility',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-prim-purp-type',
};
const research_study_prim_purp_type_Diagnostic: Coding = {
  code: 'diagnostic',
  display: 'Diagnostic',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-prim-purp-type',
};
const research_study_prim_purp_type_HealthServicesResearch: Coding = {
  code: 'health-services-research',
  display: 'Health Services Research',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-prim-purp-type',
};
const research_study_prim_purp_type_Prevention: Coding = {
  code: 'prevention',
  display: 'Prevention',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-prim-purp-type',
};
const research_study_prim_purp_type_Screening: Coding = {
  code: 'screening',
  display: 'Screening',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-prim-purp-type',
};
const research_study_prim_purp_type_SupportiveCare: Coding = {
  code: 'supportive-care',
  display: 'Supportive Care',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-prim-purp-type',
};
const research_study_prim_purp_type_Treatment: Coding = {
  code: 'treatment',
  display: 'Treatment',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-prim-purp-type',
};
/**
 * Codes for the main intent of the study.
 */
export const ResearchStudyPrimPurpType = {
  /**
   * One or more interventions for examining the basic mechanism of action (for example, physiology or biomechanics of an intervention).
   */
  BasicScience: research_study_prim_purp_type_BasicScience,
  /**
   * An intervention of a device product is being evaluated to determine the feasibility of the product or to test a prototype device and not health outcomes. Such studies are conducted to confirm the design and operating specifications of a device before beginning a full clinical trial.
   */
  DeviceFeasibility: research_study_prim_purp_type_DeviceFeasibility,
  /**
   * One or more interventions are being evaluated for identifying a disease or health condition.
   */
  Diagnostic: research_study_prim_purp_type_Diagnostic,
  /**
   * One or more interventions for evaluating the delivery, processes, management, organization, or financing of healthcare.
   */
  HealthServicesResearch: research_study_prim_purp_type_HealthServicesResearch,
  /**
   * One or more interventions are being assessed for preventing the development of a specific disease or health condition.
   */
  Prevention: research_study_prim_purp_type_Prevention,
  /**
   * One or more interventions are assessed or examined for identifying a condition, or risk factors for a condition, in people who are not yet known to have the condition or risk factor.
   */
  Screening: research_study_prim_purp_type_Screening,
  /**
   * One or more interventions are evaluated for maximizing comfort, minimizing side effects, or mitigating against a decline in the participant's health or function.
   */
  SupportiveCare: research_study_prim_purp_type_SupportiveCare,
  /**
   * One or more interventions are being evaluated for treating a disease, syndrome, or condition.
   */
  Treatment: research_study_prim_purp_type_Treatment,
};
const research_study_reason_stopped_AccrualGoalMet: Coding = {
  code: 'accrual-goal-met',
  display: 'Accrual Goal Met',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-reason-stopped',
};
const research_study_reason_stopped_ClosedDueToLackOfStudyProgress: Coding = {
  code: 'closed-due-to-lack-of-study-progress',
  display: 'Closed due to lack of study progress',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-reason-stopped',
};
const research_study_reason_stopped_ClosedDueToToxicity: Coding = {
  code: 'closed-due-to-toxicity',
  display: 'Closed due to toxicity',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-reason-stopped',
};
const research_study_reason_stopped_TemporarilyClosedPerStudyDesign: Coding = {
  code: 'temporarily-closed-per-study-design',
  display: 'Temporarily closed per study design',
  system: 'http://terminology.hl7.org/CodeSystem/research-study-reason-stopped',
};
/**
 * Codes for why the study ended prematurely.
 */
export const ResearchStudyReasonStopped = {
  /**
   * The study prematurely ended because the accrual goal was met.
   */
  AccrualGoalMet: research_study_reason_stopped_AccrualGoalMet,
  /**
   * The study prematurely ended due to lack of study progress.
   */
  ClosedDueToLackOfStudyProgress: research_study_reason_stopped_ClosedDueToLackOfStudyProgress,
  /**
   * The study prematurely ended due to toxicity.
   */
  ClosedDueToToxicity: research_study_reason_stopped_ClosedDueToToxicity,
  /**
   * The study prematurely ended temporarily per study design.
   */
  TemporarilyClosedPerStudyDesign: research_study_reason_stopped_TemporarilyClosedPerStudyDesign,
};
const research_study_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/research-study-status',
};
const research_study_status_AdministrativelyCompleted: Coding = {
  code: 'administratively-completed',
  display: 'Administratively Completed',
  system: 'http://hl7.org/fhir/research-study-status',
};
const research_study_status_Approved: Coding = {
  code: 'approved',
  display: 'Approved',
  system: 'http://hl7.org/fhir/research-study-status',
};
const research_study_status_ClosedToAccrual: Coding = {
  code: 'closed-to-accrual',
  display: 'Closed to Accrual',
  system: 'http://hl7.org/fhir/research-study-status',
};
const research_study_status_ClosedToAccrualAndIntervention: Coding = {
  code: 'closed-to-accrual-and-intervention',
  display: 'Closed to Accrual and Intervention',
  system: 'http://hl7.org/fhir/research-study-status',
};
const research_study_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/research-study-status',
};
const research_study_status_Disapproved: Coding = {
  code: 'disapproved',
  display: 'Disapproved',
  system: 'http://hl7.org/fhir/research-study-status',
};
const research_study_status_InReview: Coding = {
  code: 'in-review',
  display: 'In Review',
  system: 'http://hl7.org/fhir/research-study-status',
};
const research_study_status_TemporarilyClosedToAccrual: Coding = {
  code: 'temporarily-closed-to-accrual',
  display: 'Temporarily Closed to Accrual',
  system: 'http://hl7.org/fhir/research-study-status',
};
const research_study_status_TemporarilyClosedToAccrualAndIntervention: Coding = {
  code: 'temporarily-closed-to-accrual-and-intervention',
  display: 'Temporarily Closed to Accrual and Intervention',
  system: 'http://hl7.org/fhir/research-study-status',
};
const research_study_status_Withdrawn: Coding = {
  code: 'withdrawn',
  display: 'Withdrawn',
  system: 'http://hl7.org/fhir/research-study-status',
};
/**
 * Codes that convey the current status of the research study.
 */
export const ResearchStudyStatus = {
  /**
   * Study is opened for accrual.
   */
  Active: research_study_status_Active,
  /**
   * Study is completed prematurely and will not resume; patients are no longer examined nor treated.
   */
  AdministrativelyCompleted: research_study_status_AdministrativelyCompleted,
  /**
   * Protocol is approved by the review board.
   */
  Approved: research_study_status_Approved,
  /**
   * Study is closed for accrual; patients can be examined and treated.
   */
  ClosedToAccrual: research_study_status_ClosedToAccrual,
  /**
   * Study is closed to accrual and intervention, i.e. the study is closed to enrollment, all study subjects have completed treatment or intervention but are still being followed according to the primary objective of the study.
   */
  ClosedToAccrualAndIntervention: research_study_status_ClosedToAccrualAndIntervention,
  /**
   * Study is closed to accrual and intervention, i.e. the study is closed to enrollment, all study subjects have completed treatment
   * or intervention but are still being followed according to the primary objective of the study.
   */
  Completed: research_study_status_Completed,
  /**
   * Protocol was disapproved by the review board.
   */
  Disapproved: research_study_status_Disapproved,
  /**
   * Protocol is submitted to the review board for approval.
   */
  InReview: research_study_status_InReview,
  /**
   * Study is temporarily closed for accrual; can be potentially resumed in the future; patients can be examined and treated.
   */
  TemporarilyClosedToAccrual: research_study_status_TemporarilyClosedToAccrual,
  /**
   * Study is temporarily closed for accrual and intervention and potentially can be resumed in the future.
   */
  TemporarilyClosedToAccrualAndIntervention: research_study_status_TemporarilyClosedToAccrualAndIntervention,
  /**
   * Protocol was withdrawn by the lead organization.
   */
  Withdrawn: research_study_status_Withdrawn,
};
const research_subject_status_Candidate: Coding = {
  code: 'candidate',
  display: 'Candidate',
  system: 'http://hl7.org/fhir/research-subject-status',
};
const research_subject_status_Eligible: Coding = {
  code: 'eligible',
  display: 'Eligible',
  system: 'http://hl7.org/fhir/research-subject-status',
};
const research_subject_status_FollowUp: Coding = {
  code: 'follow-up',
  display: 'Follow-up',
  system: 'http://hl7.org/fhir/research-subject-status',
};
const research_subject_status_Ineligible: Coding = {
  code: 'ineligible',
  display: 'Ineligible',
  system: 'http://hl7.org/fhir/research-subject-status',
};
const research_subject_status_NotRegistered: Coding = {
  code: 'not-registered',
  display: 'Not Registered',
  system: 'http://hl7.org/fhir/research-subject-status',
};
const research_subject_status_OffStudy: Coding = {
  code: 'off-study',
  display: 'Off-study',
  system: 'http://hl7.org/fhir/research-subject-status',
};
const research_subject_status_OnStudy: Coding = {
  code: 'on-study',
  display: 'On-study',
  system: 'http://hl7.org/fhir/research-subject-status',
};
const research_subject_status_OnStudyIntervention: Coding = {
  code: 'on-study-intervention',
  display: 'On-study-intervention',
  system: 'http://hl7.org/fhir/research-subject-status',
};
const research_subject_status_OnStudyObservation: Coding = {
  code: 'on-study-observation',
  display: 'On-study-observation',
  system: 'http://hl7.org/fhir/research-subject-status',
};
const research_subject_status_PendingOnStudy: Coding = {
  code: 'pending-on-study',
  display: 'Pending on-study',
  system: 'http://hl7.org/fhir/research-subject-status',
};
const research_subject_status_PotentialCandidate: Coding = {
  code: 'potential-candidate',
  display: 'Potential Candidate',
  system: 'http://hl7.org/fhir/research-subject-status',
};
const research_subject_status_Screening: Coding = {
  code: 'screening',
  display: 'Screening',
  system: 'http://hl7.org/fhir/research-subject-status',
};
const research_subject_status_Withdrawn: Coding = {
  code: 'withdrawn',
  display: 'Withdrawn',
  system: 'http://hl7.org/fhir/research-subject-status',
};
/**
 * Indicates the progression of a study subject through a study.
 */
export const ResearchSubjectStatus = {
  /**
   * An identified person that can be considered for inclusion in a study.
   */
  Candidate: research_subject_status_Candidate,
  /**
   * A person that has met the eligibility criteria for inclusion in a study.
   */
  Eligible: research_subject_status_Eligible,
  /**
   * A person is no longer receiving study intervention and/or being evaluated with tests and procedures according to the protocol, but they are being monitored on a protocol-prescribed schedule.
   */
  FollowUp: research_subject_status_FollowUp,
  /**
   * A person who did not meet one or more criteria required for participation in a study is considered to have failed screening or
   * is ineligible for the study.
   */
  Ineligible: research_subject_status_Ineligible,
  /**
   * A person for whom registration was not completed.
   */
  NotRegistered: research_subject_status_NotRegistered,
  /**
   * A person that has ended their participation on a study either because their treatment/observation is complete or through not
   * responding, withdrawal, non-compliance and/or adverse event.
   */
  OffStudy: research_subject_status_OffStudy,
  /**
   * A person that is enrolled or registered on a study.
   */
  OnStudy: research_subject_status_OnStudy,
  /**
   * The person is receiving the treatment or participating in an activity (e.g. yoga, diet, etc.) that the study is evaluating.
   */
  OnStudyIntervention: research_subject_status_OnStudyIntervention,
  /**
   * The subject is being evaluated via tests and assessments according to the study calendar, but is not receiving any intervention. Note that this state is study-dependent and might not exist in all studies.  A synonym for this is "short-term follow-up".
   */
  OnStudyObservation: research_subject_status_OnStudyObservation,
  /**
   * A person is pre-registered for a study.
   */
  PendingOnStudy: research_subject_status_PendingOnStudy,
  /**
   * A person that is potentially eligible for participation in the study.
   */
  PotentialCandidate: research_subject_status_PotentialCandidate,
  /**
   * A person who is being evaluated for eligibility for a study.
   */
  Screening: research_subject_status_Screening,
  /**
   * The person has withdrawn their participation in the study before registration.
   */
  Withdrawn: research_subject_status_Withdrawn,
};
const resource_aggregation_mode_Bundled: Coding = {
  code: 'bundled',
  display: 'Bundled',
  system: 'http://hl7.org/fhir/resource-aggregation-mode',
};
const resource_aggregation_mode_Contained: Coding = {
  code: 'contained',
  display: 'Contained',
  system: 'http://hl7.org/fhir/resource-aggregation-mode',
};
const resource_aggregation_mode_Referenced: Coding = {
  code: 'referenced',
  display: 'Referenced',
  system: 'http://hl7.org/fhir/resource-aggregation-mode',
};
/**
 * How resource references can be aggregated.
 */
export const ResourceAggregationMode = {
  /**
   * The resource the reference points to will be found in the same bundle as the resource that includes the reference.
   */
  Bundled: resource_aggregation_mode_Bundled,
  /**
   * The reference is a local reference to a contained resource.
   */
  Contained: resource_aggregation_mode_Contained,
  /**
   * The reference to a resource that has to be resolved externally to the resource that includes the reference.
   */
  Referenced: resource_aggregation_mode_Referenced,
};
const resource_security_category_AnonymousREADAccessResource: Coding = {
  code: 'anonymous',
  display: 'Anonymous READ Access Resource',
  system: 'http://terminology.hl7.org/CodeSystem/resource-security-category',
};
const resource_security_category_BusinessSensitiveResource: Coding = {
  code: 'business',
  display: 'Business Sensitive Resource',
  system: 'http://terminology.hl7.org/CodeSystem/resource-security-category',
};
const resource_security_category_IndividualSensitiveResource: Coding = {
  code: 'individual',
  display: 'Individual Sensitive Resource',
  system: 'http://terminology.hl7.org/CodeSystem/resource-security-category',
};
const resource_security_category_NotClassified: Coding = {
  code: 'not-classified',
  display: 'Not classified',
  system: 'http://terminology.hl7.org/CodeSystem/resource-security-category',
};
const resource_security_category_PatientSensitive: Coding = {
  code: 'patient',
  display: 'Patient Sensitive',
  system: 'http://terminology.hl7.org/CodeSystem/resource-security-category',
};
/**
 * Provides general guidance around the kind of access Control to Read, Search, Create, Update, or Delete a resource.
 */
export const ResourceSecurityCategory = {
  /**
   * These resources tend to not contain any individual data, or business sensitive data. Most often these Resources will be available for anonymous access, meaning there is no access control based on the user or system requesting. However these Resources do tend to contain important information that must be authenticated back to the source publishing them, and protected from integrity failures in communication. For this reason server authenticated https (TLS) is recommended to provide authentication of the server and integrity protection in transit. This is normal web-server use of https.
   */
  AnonymousREADAccessResource: resource_security_category_AnonymousREADAccessResource,
  /**
   * These Resources tend to not contain any individual data, but do have data that describe business or service sensitive data. The use of the term Business is not intended to only mean an incorporated business, but rather the more broad concept of an organization, location, or other group that is not identifable as individuals. Often these resources will require some for of client authentication to assure that only authorized access is given. The client access control may be to individuals, or may be to system identity. For this purpose possible client authentication methods such as: mutual-authenticated-TLS, APIKey, App signed JWT, or App OAuth client-id JWT For example: a App that uses a Business protected Provider Directory to determine other business endpoint details.
   */
  BusinessSensitiveResource: resource_security_category_BusinessSensitiveResource,
  /**
   * These Resources do NOT contain Patient data, but do contain individual information about other participants. These other individuals are Practitioners, PractionerRole, CareTeam, or other users. These identities are needed to enable the practice of healthcare. These identities are identities under general privacy regulations, and thus must consider Privacy risk. Often access to these other identities are covered by business relationships. For this purpose access to these Resources will tend to be Role specific using methods such as RBAC or ABAC.
   */
  IndividualSensitiveResource: resource_security_category_IndividualSensitiveResource,
  /**
   * Some Resources can be used for a wide scope of use-cases that span very sensitive to very non-sensitive. These Resources do not fall into any of the above classifications, as their sensitivity is highly variable. These Resources will need special handling. These Resources often contain metadata that describes the content in a way that can be used for Access Control decisions.
   */
  NotClassified: resource_security_category_NotClassified,
  /**
   * These Resources make up the bulk of FHIR and therefore are the most commonly understood. These Resources contain highly sesitive health information, or are closely linked to highly sensitive health information. These Resources will often use the security labels to differentiate various confidentiality levels within this broad group of Patient Sensitive data. Access to these Resources often requires a declared Purpose Of Use. Access to these Resources is often controlled by a Privacy Consent.
   */
  PatientSensitive: resource_security_category_PatientSensitive,
};
const resource_slicing_rules_Closed: Coding = {
  code: 'closed',
  display: 'Closed',
  system: 'http://hl7.org/fhir/resource-slicing-rules',
};
const resource_slicing_rules_Open: Coding = {
  code: 'open',
  display: 'Open',
  system: 'http://hl7.org/fhir/resource-slicing-rules',
};
const resource_slicing_rules_OpenAtEnd: Coding = {
  code: 'openAtEnd',
  display: 'Open at End',
  system: 'http://hl7.org/fhir/resource-slicing-rules',
};
/**
 * How slices are interpreted when evaluating an instance.
 */
export const ResourceSlicingRules = {
  /**
   * No additional content is allowed other than that described by the slices in this profile.
   */
  Closed: resource_slicing_rules_Closed,
  /**
   * Additional content is allowed anywhere in the list.
   */
  Open: resource_slicing_rules_Open,
  /**
   * Additional content is allowed, but only at the end of the list. Note that using this requires that the slices be ordered, which makes it hard to share uses. This should only be done where absolutely required.
   */
  OpenAtEnd: resource_slicing_rules_OpenAtEnd,
};
const resource_status_Abandoned: Coding = {
  code: 'abandoned',
  display: 'abandoned',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Accepted: Coding = {
  code: 'accepted',
  display: 'accepted',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Active: Coding = {
  code: 'active',
  display: 'active',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_AheadOfTarget: Coding = {
  code: 'ahead-of-target',
  display: 'ahead-of-target',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Arrived: Coding = {
  code: 'arrived',
  display: 'arrived',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_BehindTarget: Coding = {
  code: 'behind-target',
  display: 'behind-target',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_BusyUnavailable: Coding = {
  code: 'busy-unavailable',
  display: 'busy-unavailable',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Complete: Coding = {
  code: 'complete',
  display: 'complete',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Confirmed: Coding = {
  code: 'confirmed',
  display: 'confirmed',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Declined: Coding = {
  code: 'declined',
  display: 'declined',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Differential: Coding = {
  code: 'differential',
  display: 'differential',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Draft: Coding = {
  code: 'draft',
  display: 'draft',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Error: Coding = {
  code: 'error',
  display: 'error',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Failed: Coding = {
  code: 'failed',
  display: 'failed',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Free: Coding = {
  code: 'free',
  display: 'free',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_HwDiscon: Coding = {
  code: 'hw-discon',
  display: 'hw-discon',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Inactive: Coding = {
  code: 'inactive',
  display: 'inactive',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_NotReady: Coding = {
  code: 'not-ready',
  display: 'not-ready',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_OnTarget: Coding = {
  code: 'on-target',
  display: 'on-target',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Partial: Coding = {
  code: 'partial',
  display: 'partial',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Planned: Coding = {
  code: 'planned',
  display: 'planned',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Proposed: Coding = {
  code: 'proposed',
  display: 'proposed',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Received: Coding = {
  code: 'received',
  display: 'received',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Refuted: Coding = {
  code: 'refuted',
  display: 'refuted',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Replaced: Coding = {
  code: 'replaced',
  display: 'replaced',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Requested: Coding = {
  code: 'requested',
  display: 'requested',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Resolved: Coding = {
  code: 'resolved',
  display: 'resolved',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Suspended: Coding = {
  code: 'suspended',
  display: 'suspended',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_TransducDiscon: Coding = {
  code: 'transduc-discon',
  display: 'transduc-discon',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Unconfirmed: Coding = {
  code: 'unconfirmed',
  display: 'unconfirmed',
  system: 'http://hl7.org/fhir/resource-status',
};
const resource_status_Unknown: Coding = {
  code: 'unknown',
  display: 'unknown',
  system: 'http://hl7.org/fhir/resource-status',
};
/**
 * The master set of status codes used throughout FHIR. All status codes are mapped to one of these codes.
 */
export const ResourceStatus = {
  /**
   * The process described/requested in the resource did not complete - usually due to some workflow error, and no further action is planned
   */
  Abandoned: resource_status_Abandoned,
  /**
   * The fulfiller has decided to perform the action, and plans are in train to do this in the future
   */
  Accepted: resource_status_Accepted,
  /**
   * The resource describes information that is currently valid or a process that is presently occuring
   */
  Active: resource_status_Active,
  /**
   * Ahead of the planned timelines
   */
  AheadOfTarget: resource_status_AheadOfTarget,
  /**
   * The pre-conditions for the action are all fulfilled, and it is imminent
   */
  Arrived: resource_status_Arrived,
  BehindTarget: resource_status_BehindTarget,
  /**
   * not available at this time/location
   */
  BusyUnavailable: resource_status_BusyUnavailable,
  /**
   * The process described/requested in the resource has been completed, and no further action is planned
   */
  Complete: resource_status_Complete,
  /**
   * The information in this resource is approved
   */
  Confirmed: resource_status_Confirmed,
  /**
   * The fulfiller chose not to perform the action
   */
  Declined: resource_status_Declined,
  /**
   * Potentially true?
   */
  Differential: resource_status_Differential,
  /**
   * The information in the resource is still being prepared and edited
   */
  Draft: resource_status_Draft,
  /**
   * The resource was created in error, and should not be treated as valid (note: in many cases, for various data integrity related reasons, the information cannot be removed from the record)
   */
  Error: resource_status_Error,
  /**
   * The process described/requested in the resource could not be completed, and no further action is planned
   */
  Failed: resource_status_Failed,
  /**
   * Free for scheduling
   */
  Free: resource_status_Free,
  /**
   * The hardware is disconnected
   */
  HwDiscon: resource_status_HwDiscon,
  /**
   * The resource describes information that is no longer valid or a process that is stopped occurring
   */
  Inactive: resource_status_Inactive,
  /**
   * Behind the planned timelines
   */
  NotReady: resource_status_NotReady,
  /**
   * Ready to act
   */
  OnTarget: resource_status_OnTarget,
  /**
   * This information is still being assembled
   */
  Partial: resource_status_Partial,
  /**
   * The resource describes a course of action that is planned and agreed/approved, but at the time of recording was still future
   */
  Planned: resource_status_Planned,
  /**
   * The resource describes an action or plan that is proposed, and not yet approved by the participants
   */
  Proposed: resource_status_Proposed,
  /**
   * The fulfiller has received the request, but not yet agreed to carry out the action
   */
  Received: resource_status_Received,
  /**
   * This information has been ruled out by testing and evaluation
   */
  Refuted: resource_status_Refuted,
  /**
   * The information in this resource has been replaced by information in another resource
   */
  Replaced: resource_status_Replaced,
  /**
   * A fulfiller has been asked to perform this action, but it has not yet occurred
   */
  Requested: resource_status_Requested,
  /**
   * The issue identified by this resource is no longer of concern
   */
  Resolved: resource_status_Resolved,
  /**
   * The process described/requested in this resource has been halted for some reason
   */
  Suspended: resource_status_Suspended,
  /**
   * The device transducer is disconnected
   */
  TransducDiscon: resource_status_TransducDiscon,
  /**
   * The information in this resource is not yet approved
   */
  Unconfirmed: resource_status_Unconfirmed,
  /**
   * Authoring system does not know the status
   */
  Unknown: resource_status_Unknown,
};
const resource_type_link_Organization: Coding = {
  code: 'organization',
  display: 'Organization',
  system: 'http://terminology.hl7.org/CodeSystem/resource-type-link',
};
const resource_type_link_Patient: Coding = {
  code: 'patient',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/resource-type-link',
};
const resource_type_link_Practitioner: Coding = {
  code: 'practitioner',
  display: 'Practitioner',
  system: 'http://terminology.hl7.org/CodeSystem/resource-type-link',
};
const resource_type_link_RelatedPerson: Coding = {
  code: 'relatedperson',
  display: 'RelatedPerson',
  system: 'http://terminology.hl7.org/CodeSystem/resource-type-link',
};
/**
 * The type of payee Resource.
 */
export const ResourceTypeLink = {
  /**
   * Organization resource.
   */
  Organization: resource_type_link_Organization,
  /**
   * Patient resource.
   */
  Patient: resource_type_link_Patient,
  /**
   * Practitioner resource.
   */
  Practitioner: resource_type_link_Practitioner,
  /**
   * RelatedPerson resource.
   */
  RelatedPerson: resource_type_link_RelatedPerson,
};
/**
 * One of the resource types defined as part of this version of FHIR.
 */
export const ResourceTypes = {
  /**
   * A financial tool for tracking value accrued for a particular purpose.  In the healthcare field, used to track charges for a patient, cost centers, etc.
   */
  Account: resource_types_Account,
  /**
   * This resource allows for the definition of some activity to be performed, independent of a particular patient, practitioner, or other performance context.
   */
  ActivityDefinition: resource_types_ActivityDefinition,
  /**
   * Actual or  potential/avoided event causing unintended physical injury resulting from or contributed to by medical care, a research study or other healthcare setting factors that requires additional monitoring, treatment, or hospitalization, or that results in death.
   */
  AdverseEvent: resource_types_AdverseEvent,
  /**
   * Risk of harmful or undesirable, physiological response which is unique to an individual and associated with exposure to a substance.
   */
  AllergyIntolerance: resource_types_AllergyIntolerance,
  /**
   * A booking of a healthcare event among patient(s), practitioner(s), related person(s) and/or device(s) for a specific date/time. This may result in one or more Encounter(s).
   */
  Appointment: resource_types_Appointment,
  /**
   * A reply to an appointment request for a patient and/or practitioner(s), such as a confirmation or rejection.
   */
  AppointmentResponse: resource_types_AppointmentResponse,
  /**
   * A record of an event made for purposes of maintaining a security log. Typical uses include detection of intrusion attempts and monitoring for inappropriate usage.
   */
  AuditEvent: resource_types_AuditEvent,
  /**
   * Basic is used for handling concepts not yet defined in FHIR, narrative-only resources that don't map to an existing resource, and custom resources not appropriate for inclusion in the FHIR specification.
   */
  Basic: resource_types_Basic,
  /**
   * A resource that represents the data of a single raw artifact as digital content accessible in its native format.  A Binary resource can contain any content, whether text, image, pdf, zip archive, etc.
   */
  Binary: resource_types_Binary,
  /**
   * A material substance originating from a biological entity intended to be transplanted or infused
   * into another (possibly the same) biological entity.
   */
  BiologicallyDerivedProduct: resource_types_BiologicallyDerivedProduct,
  /**
   * Record details about an anatomical structure.  This resource may be used when a coded concept does not provide the necessary detail needed for the use case.
   */
  BodyStructure: resource_types_BodyStructure,
  /**
   * A container for a collection of resources.
   */
  Bundle: resource_types_Bundle,
  /**
   * A Capability Statement documents a set of capabilities (behaviors) of a FHIR Server for a particular version of FHIR that may be used as a statement of actual server functionality or a statement of required or desired server implementation.
   */
  CapabilityStatement: resource_types_CapabilityStatement,
  /**
   * Describes the intention of how one or more practitioners intend to deliver care for a particular patient, group or community for a period of time, possibly limited to care for a specific condition or set of conditions.
   */
  CarePlan: resource_types_CarePlan,
  /**
   * The Care Team includes all the people and organizations who plan to participate in the coordination and delivery of care for a patient.
   */
  CareTeam: resource_types_CareTeam,
  /**
   * Catalog entries are wrappers that contextualize items included in a catalog.
   */
  CatalogEntry: resource_types_CatalogEntry,
  /**
   * The resource ChargeItem describes the provision of healthcare provider products for a certain patient, therefore referring not only to the product, but containing in addition details of the provision, like date, time, amounts and participating organizations and persons. Main Usage of the ChargeItem is to enable the billing process and internal cost allocation.
   */
  ChargeItem: resource_types_ChargeItem,
  /**
   * The ChargeItemDefinition resource provides the properties that apply to the (billing) codes necessary to calculate costs and prices. The properties may differ largely depending on type and realm, therefore this resource gives only a rough structure and requires profiling for each type of billing code system.
   */
  ChargeItemDefinition: resource_types_ChargeItemDefinition,
  /**
   * A provider issued list of professional services and products which have been provided, or are to be provided, to a patient which is sent to an insurer for reimbursement.
   */
  Claim: resource_types_Claim,
  /**
   * This resource provides the adjudication details from the processing of a Claim resource.
   */
  ClaimResponse: resource_types_ClaimResponse,
  /**
   * A record of a clinical assessment performed to determine what problem(s) may affect the patient and before planning the treatments or management strategies that are best to manage a patient's condition. Assessments are often 1:1 with a clinical consultation / encounter,  but this varies greatly depending on the clinical workflow. This resource is called "ClinicalImpression" rather than "ClinicalAssessment" to avoid confusion with the recording of assessment tools such as Apgar score.
   */
  ClinicalImpression: resource_types_ClinicalImpression,
  /**
   * The CodeSystem resource is used to declare the existence of and describe a code system or code system supplement and its key properties, and optionally define a part or all of its content.
   */
  CodeSystem: resource_types_CodeSystem,
  /**
   * An occurrence of information being transmitted; e.g. an alert that was sent to a responsible provider, a public health agency that was notified about a reportable condition.
   */
  Communication: resource_types_Communication,
  /**
   * A request to convey information; e.g. the CDS system proposes that an alert be sent to a responsible provider, the CDS system proposes that the public health agency be notified about a reportable condition.
   */
  CommunicationRequest: resource_types_CommunicationRequest,
  /**
   * A compartment definition that defines how resources are accessed on a server.
   */
  CompartmentDefinition: resource_types_CompartmentDefinition,
  /**
   * A set of healthcare-related information that is assembled together into a single logical package that provides a single coherent statement of meaning, establishes its own context and that has clinical attestation with regard to who is making the statement. A Composition defines the structure and narrative content necessary for a document. However, a Composition alone does not constitute a document. Rather, the Composition must be the first entry in a Bundle where Bundle.type=document, and any other resources referenced from Composition must be included as subsequent entries in the Bundle (for example Patient, Practitioner, Encounter, etc.).
   */
  Composition: resource_types_Composition,
  /**
   * A statement of relationships from one set of concepts to one or more other concepts - either concepts in code systems, or data element/data element concepts, or classes in class models.
   */
  ConceptMap: resource_types_ConceptMap,
  /**
   * A clinical condition, problem, diagnosis, or other event, situation, issue, or clinical concept that has risen to a level of concern.
   */
  Condition: resource_types_Condition,
  /**
   * A record of a healthcare consumer’s  choices, which permits or denies identified recipient(s) or recipient role(s) to perform one or more actions within a given policy context, for specific purposes and periods of time.
   */
  Consent: resource_types_Consent,
  /**
   * Legally enforceable, formally recorded unilateral or bilateral directive i.e., a policy or agreement.
   */
  Contract: resource_types_Contract,
  /**
   * Financial instrument which may be used to reimburse or pay for health care products and services. Includes both insurance and self-payment.
   */
  Coverage: resource_types_Coverage,
  /**
   * The CoverageEligibilityRequest provides patient and insurance coverage information to an insurer for them to respond, in the form of an CoverageEligibilityResponse, with information regarding whether the stated coverage is valid and in-force and optionally to provide the insurance details of the policy.
   */
  CoverageEligibilityRequest: resource_types_CoverageEligibilityRequest,
  /**
   * This resource provides eligibility and plan details from the processing of an CoverageEligibilityRequest resource.
   */
  CoverageEligibilityResponse: resource_types_CoverageEligibilityResponse,
  /**
   * Indicates an actual or potential clinical issue with or between one or more active or proposed clinical actions for a patient; e.g. Drug-drug interaction, Ineffective treatment frequency, Procedure-condition conflict, etc.
   */
  DetectedIssue: resource_types_DetectedIssue,
  /**
   * A type of a manufactured item that is used in the provision of healthcare without being substantially changed through that activity. The device may be a medical or non-medical device.
   */
  Device: resource_types_Device,
  /**
   * The characteristics, operational status and capabilities of a medical-related component of a medical device.
   */
  DeviceDefinition: resource_types_DeviceDefinition,
  /**
   * Describes a measurement, calculation or setting capability of a medical device.
   */
  DeviceMetric: resource_types_DeviceMetric,
  /**
   * Represents a request for a patient to employ a medical device. The device may be an implantable device, or an external assistive device, such as a walker.
   */
  DeviceRequest: resource_types_DeviceRequest,
  /**
   * A record of a device being used by a patient where the record is the result of a report from the patient or another clinician.
   */
  DeviceUseStatement: resource_types_DeviceUseStatement,
  /**
   * The findings and interpretation of diagnostic  tests performed on patients, groups of patients, devices, and locations, and/or specimens derived from these. The report includes clinical context such as requesting and provider information, and some mix of atomic results, images, textual and coded interpretations, and formatted representation of diagnostic reports.
   */
  DiagnosticReport: resource_types_DiagnosticReport,
  /**
   * A collection of documents compiled for a purpose together with metadata that applies to the collection.
   */
  DocumentManifest: resource_types_DocumentManifest,
  /**
   * A reference to a document of any kind for any purpose. Provides metadata about the document so that the document can be discovered and managed. The scope of a document is any seralized object with a mime-type, so includes formal patient centric documents (CDA), cliical notes, scanned paper, and non-patient specific documents like policy text.
   */
  DocumentReference: resource_types_DocumentReference,
  /**
   * A resource that includes narrative, extensions, and contained resources.
   */
  DomainResource: resource_types_DomainResource,
  /**
   * The EffectEvidenceSynthesis resource describes the difference in an outcome between exposures states in a population where the effect estimate is derived from a combination of research studies.
   */
  EffectEvidenceSynthesis: resource_types_EffectEvidenceSynthesis,
  /**
   * An interaction between a patient and healthcare provider(s) for the purpose of providing healthcare service(s) or assessing the health status of a patient.
   */
  Encounter: resource_types_Encounter,
  /**
   * The technical details of an endpoint that can be used for electronic services, such as for web services providing XDS.b or a REST endpoint for another FHIR server. This may include any security context information.
   */
  Endpoint: resource_types_Endpoint,
  /**
   * This resource provides the insurance enrollment details to the insurer regarding a specified coverage.
   */
  EnrollmentRequest: resource_types_EnrollmentRequest,
  /**
   * This resource provides enrollment and plan details from the processing of an EnrollmentRequest resource.
   */
  EnrollmentResponse: resource_types_EnrollmentResponse,
  /**
   * An association between a patient and an organization / healthcare provider(s) during which time encounters may occur. The managing organization assumes a level of responsibility for the patient during this time.
   */
  EpisodeOfCare: resource_types_EpisodeOfCare,
  /**
   * The EventDefinition resource provides a reusable description of when a particular event can occur.
   */
  EventDefinition: resource_types_EventDefinition,
  /**
   * The Evidence resource describes the conditional state (population and any exposures being compared within the population) and outcome (if specified) that the knowledge (evidence, assertion, recommendation) is about.
   */
  Evidence: resource_types_Evidence,
  /**
   * The EvidenceVariable resource describes a "PICO" element that knowledge (evidence, assertion, recommendation) is about.
   */
  EvidenceVariable: resource_types_EvidenceVariable,
  /**
   * Example of workflow instance.
   */
  ExampleScenario: resource_types_ExampleScenario,
  /**
   * This resource provides: the claim details; adjudication details from the processing of a Claim; and optionally account balance information, for informing the subscriber of the benefits provided.
   */
  ExplanationOfBenefit: resource_types_ExplanationOfBenefit,
  /**
   * Significant health conditions for a person related to the patient relevant in the context of care for the patient.
   */
  FamilyMemberHistory: resource_types_FamilyMemberHistory,
  /**
   * Prospective warnings of potential issues when providing care to the patient.
   */
  Flag: resource_types_Flag,
  /**
   * Describes the intended objective(s) for a patient, group or organization care, for example, weight loss, restoring an activity of daily living, obtaining herd immunity via immunization, meeting a process improvement objective, etc.
   */
  Goal: resource_types_Goal,
  /**
   * A formal computable definition of a graph of resources - that is, a coherent set of resources that form a graph by following references. The Graph Definition resource defines a set and makes rules about the set.
   */
  GraphDefinition: resource_types_GraphDefinition,
  /**
   * Represents a defined collection of entities that may be discussed or acted upon collectively but which are not expected to act collectively, and are not formally or legally recognized; i.e. a collection of entities that isn't an Organization.
   */
  Group: resource_types_Group,
  /**
   * A guidance response is the formal response to a guidance request, including any output parameters returned by the evaluation, as well as the description of any proposed actions to be taken.
   */
  GuidanceResponse: resource_types_GuidanceResponse,
  /**
   * The details of a healthcare service available at a location.
   */
  HealthcareService: resource_types_HealthcareService,
  /**
   * Representation of the content produced in a DICOM imaging study. A study comprises a set of series, each of which includes a set of Service-Object Pair Instances (SOP Instances - images or other data) acquired or produced in a common context.  A series is of only one modality (e.g. X-ray, CT, MR, ultrasound), but a study may have multiple series of different modalities.
   */
  ImagingStudy: resource_types_ImagingStudy,
  /**
   * Describes the event of a patient being administered a vaccine or a record of an immunization as reported by a patient, a clinician or another party.
   */
  Immunization: resource_types_Immunization,
  /**
   * Describes a comparison of an immunization event against published recommendations to determine if the administration is "valid" in relation to those  recommendations.
   */
  ImmunizationEvaluation: resource_types_ImmunizationEvaluation,
  /**
   * A patient's point-in-time set of recommendations (i.e. forecasting) according to a published schedule with optional supporting justification.
   */
  ImmunizationRecommendation: resource_types_ImmunizationRecommendation,
  /**
   * A set of rules of how a particular interoperability or standards problem is solved - typically through the use of FHIR resources. This resource is used to gather all the parts of an implementation guide into a logical whole and to publish a computable definition of all the parts.
   */
  ImplementationGuide: resource_types_ImplementationGuide,
  /**
   * Details of a Health Insurance product/plan provided by an organization.
   */
  InsurancePlan: resource_types_InsurancePlan,
  /**
   * Invoice containing collected ChargeItems from an Account with calculated individual and total price for Billing purpose.
   */
  Invoice: resource_types_Invoice,
  /**
   * The Library resource is a general-purpose container for knowledge asset definitions. It can be used to describe and expose existing knowledge assets such as logic libraries and information model descriptions, as well as to describe a collection of knowledge assets.
   */
  Library: resource_types_Library,
  /**
   * Identifies two or more records (resource instances) that refer to the same real-world "occurrence".
   */
  Linkage: resource_types_Linkage,
  /**
   * A list is a curated collection of resources.
   */
  List: resource_types_List,
  /**
   * Details and position information for a physical place where services are provided and resources and participants may be stored, found, contained, or accommodated.
   */
  Location: resource_types_Location,
  /**
   * The Measure resource provides the definition of a quality measure.
   */
  Measure: resource_types_Measure,
  /**
   * The MeasureReport resource contains the results of the calculation of a measure; and optionally a reference to the resources involved in that calculation.
   */
  MeasureReport: resource_types_MeasureReport,
  /**
   * A photo, video, or audio recording acquired or used in healthcare. The actual content may be inline or provided by direct reference.
   */
  Media: resource_types_Media,
  /**
   * This resource is primarily used for the identification and definition of a medication for the purposes of prescribing, dispensing, and administering a medication as well as for making statements about medication use.
   */
  Medication: resource_types_Medication,
  /**
   * Describes the event of a patient consuming or otherwise being administered a medication.  This may be as simple as swallowing a tablet or it may be a long running infusion.  Related resources tie this event to the authorizing prescription, and the specific encounter between patient and health care practitioner.
   */
  MedicationAdministration: resource_types_MedicationAdministration,
  /**
   * Indicates that a medication product is to be or has been dispensed for a named person/patient.  This includes a description of the medication product (supply) provided and the instructions for administering the medication.  The medication dispense is the result of a pharmacy system responding to a medication order.
   */
  MedicationDispense: resource_types_MedicationDispense,
  /**
   * Information about a medication that is used to support knowledge.
   */
  MedicationKnowledge: resource_types_MedicationKnowledge,
  /**
   * An order or request for both supply of the medication and the instructions for administration of the medication to a patient. The resource is called "MedicationRequest" rather than "MedicationPrescription" or "MedicationOrder" to generalize the use across inpatient and outpatient settings, including care plans, etc., and to harmonize with workflow patterns.
   */
  MedicationRequest: resource_types_MedicationRequest,
  /**
   * A record of a medication that is being consumed by a patient.   A MedicationStatement may indicate that the patient may be taking the medication now or has taken the medication in the past or will be taking the medication in the future.  The source of this information can be the patient, significant other (such as a family member or spouse), or a clinician.  A common scenario where this information is captured is during the history taking process during a patient visit or stay.   The medication information may come from sources such as the patient's memory, from a prescription bottle,  or from a list of medications the patient, clinician or other party maintains.
   * The primary difference between a medication statement and a medication administration is that the medication administration has complete administration information and is based on actual administration information from the person who administered the medication.  A medication statement is often, if not always, less specific.  There is no required date/time when the medication was administered, in fact we only know that a source has reported the patient is taking this medication, where details such as time, quantity, or rate or even medication product may be incomplete or missing or less precise.  As stated earlier, the medication statement information may come from the patient's memory, from a prescription bottle or from a list of medications the patient, clinician or other party maintains.  Medication administration is more formal and is not missing detailed information.
   */
  MedicationStatement: resource_types_MedicationStatement,
  /**
   * Detailed definition of a medicinal product, typically for uses other than direct patient care (e.g. regulatory use).
   */
  MedicinalProduct: resource_types_MedicinalProduct,
  /**
   * The regulatory authorization of a medicinal product.
   */
  MedicinalProductAuthorization: resource_types_MedicinalProductAuthorization,
  /**
   * The clinical particulars - indications, contraindications etc. of a medicinal product, including for regulatory purposes.
   */
  MedicinalProductContraindication: resource_types_MedicinalProductContraindication,
  /**
   * Indication for the Medicinal Product.
   */
  MedicinalProductIndication: resource_types_MedicinalProductIndication,
  /**
   * An ingredient of a manufactured item or pharmaceutical product.
   */
  MedicinalProductIngredient: resource_types_MedicinalProductIngredient,
  /**
   * The interactions of the medicinal product with other medicinal products, or other forms of interactions.
   */
  MedicinalProductInteraction: resource_types_MedicinalProductInteraction,
  /**
   * The manufactured item as contained in the packaged medicinal product.
   */
  MedicinalProductManufactured: resource_types_MedicinalProductManufactured,
  /**
   * A medicinal product in a container or package.
   */
  MedicinalProductPackaged: resource_types_MedicinalProductPackaged,
  /**
   * A pharmaceutical product described in terms of its composition and dose form.
   */
  MedicinalProductPharmaceutical: resource_types_MedicinalProductPharmaceutical,
  /**
   * Describe the undesirable effects of the medicinal product.
   */
  MedicinalProductUndesirableEffect: resource_types_MedicinalProductUndesirableEffect,
  /**
   * Defines the characteristics of a message that can be shared between systems, including the type of event that initiates the message, the content to be transmitted and what response(s), if any, are permitted.
   */
  MessageDefinition: resource_types_MessageDefinition,
  /**
   * The header for a message exchange that is either requesting or responding to an action.  The reference(s) that are the subject of the action as well as other information related to the action are typically transmitted in a bundle in which the MessageHeader resource instance is the first resource in the bundle.
   */
  MessageHeader: resource_types_MessageHeader,
  /**
   * Raw data describing a biological sequence.
   */
  MolecularSequence: resource_types_MolecularSequence,
  /**
   * A curated namespace that issues unique symbols within that namespace for the identification of concepts, people, devices, etc.  Represents a "System" used within the Identifier and Coding data types.
   */
  NamingSystem: resource_types_NamingSystem,
  /**
   * A request to supply a diet, formula feeding (enteral) or oral nutritional supplement to a patient/resident.
   */
  NutritionOrder: resource_types_NutritionOrder,
  /**
   * Measurements and simple assertions made about a patient, device or other subject.
   */
  Observation: resource_types_Observation,
  /**
   * Set of definitional characteristics for a kind of observation or measurement produced or consumed by an orderable health care service.
   */
  ObservationDefinition: resource_types_ObservationDefinition,
  /**
   * A formal computable definition of an operation (on the RESTful interface) or a named query (using the search interaction).
   */
  OperationDefinition: resource_types_OperationDefinition,
  /**
   * A collection of error, warning, or information messages that result from a system action.
   */
  OperationOutcome: resource_types_OperationOutcome,
  /**
   * A formally or informally recognized grouping of people or organizations formed for the purpose of achieving some form of collective action.  Includes companies, institutions, corporations, departments, community groups, healthcare practice groups, payer/insurer, etc.
   */
  Organization: resource_types_Organization,
  /**
   * Defines an affiliation/assotiation/relationship between 2 distinct oganizations, that is not a part-of relationship/sub-division relationship.
   */
  OrganizationAffiliation: resource_types_OrganizationAffiliation,
  /**
   * This resource is a non-persisted resource used to pass information into and back from an [operation](operations.html). It has no other use, and there is no RESTful endpoint associated with it.
   */
  Parameters: resource_types_Parameters,
  /**
   * Demographics and other administrative information about an individual or animal receiving care or other health-related services.
   */
  Patient: resource_types_Patient,
  /**
   * This resource provides the status of the payment for goods and services rendered, and the request and response resource references.
   */
  PaymentNotice: resource_types_PaymentNotice,
  /**
   * This resource provides the details including amount of a payment and allocates the payment items being paid.
   */
  PaymentReconciliation: resource_types_PaymentReconciliation,
  /**
   * Demographics and administrative information about a person independent of a specific health-related context.
   */
  Person: resource_types_Person,
  /**
   * This resource allows for the definition of various types of plans as a sharable, consumable, and executable artifact. The resource is general enough to support the description of a broad range of clinical artifacts such as clinical decision support rules, order sets and protocols.
   */
  PlanDefinition: resource_types_PlanDefinition,
  /**
   * A person who is directly or indirectly involved in the provisioning of healthcare.
   */
  Practitioner: resource_types_Practitioner,
  /**
   * A specific set of Roles/Locations/specialties/services that a practitioner may perform at an organization for a period of time.
   */
  PractitionerRole: resource_types_PractitionerRole,
  /**
   * An action that is or was performed on or for a patient. This can be a physical intervention like an operation, or less invasive like long term services, counseling, or hypnotherapy.
   */
  Procedure: resource_types_Procedure,
  /**
   * Provenance of a resource is a record that describes entities and processes involved in producing and delivering or otherwise influencing that resource. Provenance provides a critical foundation for assessing authenticity, enabling trust, and allowing reproducibility. Provenance assertions are a form of contextual metadata and can themselves become important records with their own provenance. Provenance statement indicates clinical significance in terms of confidence in authenticity, reliability, and trustworthiness, integrity, and stage in lifecycle (e.g. Document Completion - has the artifact been legally authenticated), all of which may impact security, privacy, and trust policies.
   */
  Provenance: resource_types_Provenance,
  /**
   * A structured set of questions intended to guide the collection of answers from end-users. Questionnaires provide detailed control over order, presentation, phraseology and grouping to allow coherent, consistent data collection.
   */
  Questionnaire: resource_types_Questionnaire,
  /**
   * A structured set of questions and their answers. The questions are ordered and grouped into coherent subsets, corresponding to the structure of the grouping of the questionnaire being responded to.
   */
  QuestionnaireResponse: resource_types_QuestionnaireResponse,
  /**
   * Information about a person that is involved in the care for a patient, but who is not the target of healthcare, nor has a formal responsibility in the care process.
   */
  RelatedPerson: resource_types_RelatedPerson,
  /**
   * A group of related requests that can be used to capture intended activities that have inter-dependencies such as "give this medication after that one".
   */
  RequestGroup: resource_types_RequestGroup,
  /**
   * The ResearchDefinition resource describes the conditional state (population and any exposures being compared within the population) and outcome (if specified) that the knowledge (evidence, assertion, recommendation) is about.
   */
  ResearchDefinition: resource_types_ResearchDefinition,
  /**
   * The ResearchElementDefinition resource describes a "PICO" element that knowledge (evidence, assertion, recommendation) is about.
   */
  ResearchElementDefinition: resource_types_ResearchElementDefinition,
  /**
   * A process where a researcher or organization plans and then executes a series of steps intended to increase the field of healthcare-related knowledge.  This includes studies of safety, efficacy, comparative effectiveness and other information about medications, devices, therapies and other interventional and investigative techniques.  A ResearchStudy involves the gathering of information about human or animal subjects.
   */
  ResearchStudy: resource_types_ResearchStudy,
  /**
   * A physical entity which is the primary unit of operational and/or administrative interest in a study.
   */
  ResearchSubject: resource_types_ResearchSubject,
  /**
   * This is the base resource type for everything.
   */
  Resource: resource_types_Resource,
  /**
   * An assessment of the likely outcome(s) for a patient or other subject as well as the likelihood of each outcome.
   */
  RiskAssessment: resource_types_RiskAssessment,
  /**
   * The RiskEvidenceSynthesis resource describes the likelihood of an outcome in a population plus exposure state where the risk estimate is derived from a combination of research studies.
   */
  RiskEvidenceSynthesis: resource_types_RiskEvidenceSynthesis,
  /**
   * A container for slots of time that may be available for booking appointments.
   */
  Schedule: resource_types_Schedule,
  /**
   * A search parameter that defines a named search item that can be used to search/filter on a resource.
   */
  SearchParameter: resource_types_SearchParameter,
  /**
   * A record of a request for service such as diagnostic investigations, treatments, or operations to be performed.
   */
  ServiceRequest: resource_types_ServiceRequest,
  /**
   * A slot of time on a schedule that may be available for booking appointments.
   */
  Slot: resource_types_Slot,
  /**
   * A sample to be used for analysis.
   */
  Specimen: resource_types_Specimen,
  /**
   * A kind of specimen with associated set of requirements.
   */
  SpecimenDefinition: resource_types_SpecimenDefinition,
  /**
   * A definition of a FHIR structure. This resource is used to describe the underlying resources, data types defined in FHIR, and also for describing extensions and constraints on resources and data types.
   */
  StructureDefinition: resource_types_StructureDefinition,
  /**
   * A Map of relationships between 2 structures that can be used to transform data.
   */
  StructureMap: resource_types_StructureMap,
  /**
   * The subscription resource is used to define a push-based subscription from a server to another system. Once a subscription is registered with the server, the server checks every resource that is created or updated, and if the resource matches the given criteria, it sends a message on the defined "channel" so that another system can take an appropriate action.
   */
  Subscription: resource_types_Subscription,
  /**
   * A homogeneous material with a definite composition.
   */
  Substance: resource_types_Substance,
  /**
   * Nucleic acids are defined by three distinct elements: the base, sugar and linkage. Individual substance/moiety IDs will be created for each of these elements. The nucleotide sequence will be always entered in the 5’-3’ direction.
   */
  SubstanceNucleicAcid: resource_types_SubstanceNucleicAcid,
  /**
   * Todo.
   */
  SubstancePolymer: resource_types_SubstancePolymer,
  /**
   * A SubstanceProtein is defined as a single unit of a linear amino acid sequence, or a combination of subunits that are either covalently linked or have a defined invariant stoichiometric relationship. This includes all synthetic, recombinant and purified SubstanceProteins of defined sequence, whether the use is therapeutic or prophylactic. This set of elements will be used to describe albumins, coagulation factors, cytokines, growth factors, peptide/SubstanceProtein hormones, enzymes, toxins, toxoids, recombinant vaccines, and immunomodulators.
   */
  SubstanceProtein: resource_types_SubstanceProtein,
  /**
   * Todo.
   */
  SubstanceReferenceInformation: resource_types_SubstanceReferenceInformation,
  /**
   * Source material shall capture information on the taxonomic and anatomical origins as well as the fraction of a material that can result in or can be modified to form a substance. This set of data elements shall be used to define polymer substances isolated from biological matrices. Taxonomic and anatomical origins shall be described using a controlled vocabulary as required. This information is captured for naturally derived polymers ( . starch) and structurally diverse substances. For Organisms belonging to the Kingdom Plantae the Substance level defines the fresh material of a single species or infraspecies, the Herbal Drug and the Herbal preparation. For Herbal preparations, the fraction information will be captured at the Substance information level and additional information for herbal extracts will be captured at the Specified Substance Group 1 information level. See for further explanation the Substance Class: Structurally Diverse and the herbal annex.
   */
  SubstanceSourceMaterial: resource_types_SubstanceSourceMaterial,
  /**
   * The detailed description of a substance, typically at a level beyond what is used for prescribing.
   */
  SubstanceSpecification: resource_types_SubstanceSpecification,
  /**
   * Record of delivery of what is supplied.
   */
  SupplyDelivery: resource_types_SupplyDelivery,
  /**
   * A record of a request for a medication, substance or device used in the healthcare setting.
   */
  SupplyRequest: resource_types_SupplyRequest,
  /**
   * A task to be performed.
   */
  Task: resource_types_Task,
  /**
   * A TerminologyCapabilities resource documents a set of capabilities (behaviors) of a FHIR Terminology Server that may be used as a statement of actual server functionality or a statement of required or desired server implementation.
   */
  TerminologyCapabilities: resource_types_TerminologyCapabilities,
  /**
   * A summary of information based on the results of executing a TestScript.
   */
  TestReport: resource_types_TestReport,
  /**
   * A structured set of tests against a FHIR server or client implementation to determine compliance against the FHIR specification.
   */
  TestScript: resource_types_TestScript,
  /**
   * A ValueSet resource instance specifies a set of codes drawn from one or more code systems, intended for use in a particular context. Value sets link between [[[CodeSystem]]] definitions and their use in [coded elements](terminologies.html).
   */
  ValueSet: resource_types_ValueSet,
  /**
   * Describes validation requirements, source(s), status and dates for one or more elements.
   */
  VerificationResult: resource_types_VerificationResult,
  /**
   * An authorization for the provision of glasses and/or contact lenses to a patient.
   */
  VisionPrescription: resource_types_VisionPrescription,
};
const resource_validation_mode_ValidateForCreate: Coding = {
  code: 'create',
  display: 'Validate for Create',
  system: 'http://hl7.org/fhir/resource-validation-mode',
};
const resource_validation_mode_ValidateForDelete: Coding = {
  code: 'delete',
  display: 'Validate for Delete',
  system: 'http://hl7.org/fhir/resource-validation-mode',
};
const resource_validation_mode_ValidateAgainstAProfile: Coding = {
  code: 'profile',
  display: 'Validate Against a Profile',
  system: 'http://hl7.org/fhir/resource-validation-mode',
};
const resource_validation_mode_ValidateForUpdate: Coding = {
  code: 'update',
  display: 'Validate for Update',
  system: 'http://hl7.org/fhir/resource-validation-mode',
};
/**
 * Codes indicating the type of validation to perform.
 */
export const ResourceValidationMode = {
  /**
   * The server checks the content, and then checks that the content would be acceptable as a create (e.g. that the content would not violate any uniqueness constraints).
   */
  ValidateForCreate: resource_validation_mode_ValidateForCreate,
  /**
   * The server ignores the content and checks that the nominated resource is allowed to be deleted (e.g. checking referential integrity rules).
   */
  ValidateForDelete: resource_validation_mode_ValidateForDelete,
  /**
   * The server checks an existing resource (must be nominated by id, not provided as a parameter) as valid against the nominated profile.
   */
  ValidateAgainstAProfile: resource_validation_mode_ValidateAgainstAProfile,
  /**
   * The server checks the content, and then checks that it would accept it as an update against the nominated specific resource (e.g. that there are no changes to immutable fields the server does not allow to change and checking version integrity if appropriate).
   */
  ValidateForUpdate: resource_validation_mode_ValidateForUpdate,
};
const response_code_FatalError: Coding = {
  code: 'fatal-error',
  display: 'Fatal Error',
  system: 'http://hl7.org/fhir/response-code',
};
const response_code_OK: Coding = {
  code: 'ok',
  display: 'OK',
  system: 'http://hl7.org/fhir/response-code',
};
const response_code_TransientError: Coding = {
  code: 'transient-error',
  display: 'Transient Error',
  system: 'http://hl7.org/fhir/response-code',
};
/**
 * The kind of response to a message.
 */
export const ResponseCode = {
  /**
   * The message was rejected because of a problem with the content. There is no point in re-sending without change. The response narrative SHALL describe the issue.
   */
  FatalError: response_code_FatalError,
  /**
   * The message was accepted and processed without error.
   */
  OK: response_code_OK,
  /**
   * Some internal unexpected error occurred - wait and try again. Note - this is usually used for things like database unavailable, which may be expected to resolve, though human intervention may be required.
   */
  TransientError: response_code_TransientError,
};
const restful_capability_mode_Client: Coding = {
  code: 'client',
  display: 'Client',
  system: 'http://hl7.org/fhir/restful-capability-mode',
};
const restful_capability_mode_Server: Coding = {
  code: 'server',
  display: 'Server',
  system: 'http://hl7.org/fhir/restful-capability-mode',
};
/**
 * The mode of a RESTful capability statement.
 */
export const RestfulCapabilityMode = {
  /**
   * The application acts as a client for this resource.
   */
  Client: restful_capability_mode_Client,
  /**
   * The application acts as a server for this resource.
   */
  Server: restful_capability_mode_Server,
};
const restful_security_service_Basic: Coding = {
  code: 'Basic',
  display: 'Basic',
  system: 'http://terminology.hl7.org/CodeSystem/restful-security-service',
};
const restful_security_service_Certificates: Coding = {
  code: 'Certificates',
  display: 'Certificates',
  system: 'http://terminology.hl7.org/CodeSystem/restful-security-service',
};
const restful_security_service_Kerberos: Coding = {
  code: 'Kerberos',
  display: 'Kerberos',
  system: 'http://terminology.hl7.org/CodeSystem/restful-security-service',
};
const restful_security_service_NTLM: Coding = {
  code: 'NTLM',
  display: 'NTLM',
  system: 'http://terminology.hl7.org/CodeSystem/restful-security-service',
};
const restful_security_service_OAuth: Coding = {
  code: 'OAuth',
  display: 'OAuth',
  system: 'http://terminology.hl7.org/CodeSystem/restful-security-service',
};
const restful_security_service_SMARTOnFHIR: Coding = {
  code: 'SMART-on-FHIR',
  display: 'SMART-on-FHIR',
  system: 'http://terminology.hl7.org/CodeSystem/restful-security-service',
};
/**
 * Types of security services used with FHIR.
 */
export const RestfulSecurityService = {
  /**
   * Basic authentication defined in HTTP specification.
   */
  Basic: restful_security_service_Basic,
  /**
   * SSL where client must have a certificate registered with the server.
   */
  Certificates: restful_security_service_Certificates,
  /**
   * see http://www.ietf.org/rfc/rfc4120.txt.
   */
  Kerberos: restful_security_service_Kerberos,
  /**
   * Microsoft NTLM Authentication.
   */
  NTLM: restful_security_service_NTLM,
  /**
   * OAuth (unspecified version see oauth.net).
   */
  OAuth: restful_security_service_OAuth,
  /**
   * OAuth2 using SMART-on-FHIR profile (see http://docs.smarthealthit.org/).
   */
  SMARTOnFHIR: restful_security_service_SMARTOnFHIR,
};
const risk_estimate_type_Count: Coding = {
  code: 'count',
  display: 'count',
  system: 'http://terminology.hl7.org/CodeSystem/risk-estimate-type',
};
const risk_estimate_type_DerivedProportion: Coding = {
  code: 'derivedProportion',
  display: 'derivedProportion',
  system: 'http://terminology.hl7.org/CodeSystem/risk-estimate-type',
};
const risk_estimate_type_Descriptive: Coding = {
  code: 'descriptive',
  display: 'descriptive',
  system: 'http://terminology.hl7.org/CodeSystem/risk-estimate-type',
};
const risk_estimate_type_Mean: Coding = {
  code: 'mean',
  display: 'mean',
  system: 'http://terminology.hl7.org/CodeSystem/risk-estimate-type',
};
const risk_estimate_type_Median: Coding = {
  code: 'median',
  display: 'median',
  system: 'http://terminology.hl7.org/CodeSystem/risk-estimate-type',
};
const risk_estimate_type_Proportion: Coding = {
  code: 'proportion',
  display: 'proportion',
  system: 'http://terminology.hl7.org/CodeSystem/risk-estimate-type',
};
/**
 * Whether the risk estimate is dichotomous, continuous or qualitative and the specific type of risk estimate (eg proportion or median).
 */
export const RiskEstimateType = {
  /**
   * descriptive measure reported as total number of items.
   */
  Count: risk_estimate_type_Count,
  /**
   * A special use case where the proportion is derived from a formula rather than derived from summary evidence.
   */
  DerivedProportion: risk_estimate_type_DerivedProportion,
  /**
   * descriptive measure reported as narrative.
   */
  Descriptive: risk_estimate_type_Descriptive,
  /**
   * continuous numerical measure reported as an average.
   */
  Mean: risk_estimate_type_Mean,
  /**
   * continuous numerical measure reported as the middle of the range.
   */
  Median: risk_estimate_type_Median,
  /**
   * dichotomous measure (present or absent) reported as a ratio compared to the denominator of 1 (A percentage is a proportion with denominator of 100).
   */
  Proportion: risk_estimate_type_Proportion,
};
const risk_probability_Certain: Coding = {
  code: 'certain',
  display: 'Certain',
  system: 'http://terminology.hl7.org/CodeSystem/risk-probability',
};
const risk_probability_HighLikelihood: Coding = {
  code: 'high',
  display: 'High likelihood',
  system: 'http://terminology.hl7.org/CodeSystem/risk-probability',
};
const risk_probability_LowLikelihood: Coding = {
  code: 'low',
  display: 'Low likelihood',
  system: 'http://terminology.hl7.org/CodeSystem/risk-probability',
};
const risk_probability_ModerateLikelihood: Coding = {
  code: 'moderate',
  display: 'Moderate likelihood',
  system: 'http://terminology.hl7.org/CodeSystem/risk-probability',
};
const risk_probability_NegligibleLikelihood: Coding = {
  code: 'negligible',
  display: 'Negligible likelihood',
  system: 'http://terminology.hl7.org/CodeSystem/risk-probability',
};
/**
 * Codes representing the likelihood of a particular outcome in a risk assessment.
 */
export const RiskProbability = {
  /**
   * The specified outcome is effectively guaranteed.
   */
  Certain: risk_probability_Certain,
  /**
   * The specified outcome is more likely to occur than not.
   */
  HighLikelihood: risk_probability_HighLikelihood,
  /**
   * The specified outcome is possible but unlikely.
   */
  LowLikelihood: risk_probability_LowLikelihood,
  /**
   * The specified outcome has a reasonable likelihood of occurrence.
   */
  ModerateLikelihood: risk_probability_ModerateLikelihood,
  /**
   * The specified outcome is exceptionally unlikely.
   */
  NegligibleLikelihood: risk_probability_NegligibleLikelihood,
};
const search_comparator_Approximately: Coding = {
  code: 'ap',
  display: 'Approximately',
  system: 'http://hl7.org/fhir/search-comparator',
};
const search_comparator_EndsBefore: Coding = {
  code: 'eb',
  display: 'Ends Before',
  system: 'http://hl7.org/fhir/search-comparator',
};
const search_comparator_Equals: Coding = {
  code: 'eq',
  display: 'Equals',
  system: 'http://hl7.org/fhir/search-comparator',
};
const search_comparator_GreaterOrEquals: Coding = {
  code: 'ge',
  display: 'Greater or Equals',
  system: 'http://hl7.org/fhir/search-comparator',
};
const search_comparator_GreaterThan: Coding = {
  code: 'gt',
  display: 'Greater Than',
  system: 'http://hl7.org/fhir/search-comparator',
};
const search_comparator_LessOfEqual: Coding = {
  code: 'le',
  display: 'Less of Equal',
  system: 'http://hl7.org/fhir/search-comparator',
};
const search_comparator_LessThan: Coding = {
  code: 'lt',
  display: 'Less Than',
  system: 'http://hl7.org/fhir/search-comparator',
};
const search_comparator_NotEquals: Coding = {
  code: 'ne',
  display: 'Not Equals',
  system: 'http://hl7.org/fhir/search-comparator',
};
const search_comparator_StartsAfter: Coding = {
  code: 'sa',
  display: 'Starts After',
  system: 'http://hl7.org/fhir/search-comparator',
};
/**
 * What Search Comparator Codes are supported in search.
 */
export const SearchComparator = {
  /**
   * the value for the parameter in the resource is approximately the same to the provided value.
   */
  Approximately: search_comparator_Approximately,
  /**
   * the value for the parameter in the resource ends before the provided value.
   */
  EndsBefore: search_comparator_EndsBefore,
  /**
   * the value for the parameter in the resource is equal to the provided value.
   */
  Equals: search_comparator_Equals,
  /**
   * the value for the parameter in the resource is greater or equal to the provided value.
   */
  GreaterOrEquals: search_comparator_GreaterOrEquals,
  /**
   * the value for the parameter in the resource is greater than the provided value.
   */
  GreaterThan: search_comparator_GreaterThan,
  /**
   * the value for the parameter in the resource is less or equal to the provided value.
   */
  LessOfEqual: search_comparator_LessOfEqual,
  /**
   * the value for the parameter in the resource is less than the provided value.
   */
  LessThan: search_comparator_LessThan,
  /**
   * the value for the parameter in the resource is not equal to the provided value.
   */
  NotEquals: search_comparator_NotEquals,
  /**
   * the value for the parameter in the resource starts after the provided value.
   */
  StartsAfter: search_comparator_StartsAfter,
};
const search_entry_mode_Include: Coding = {
  code: 'include',
  display: 'Include',
  system: 'http://hl7.org/fhir/search-entry-mode',
};
const search_entry_mode_Match: Coding = {
  code: 'match',
  display: 'Match',
  system: 'http://hl7.org/fhir/search-entry-mode',
};
const search_entry_mode_Outcome: Coding = {
  code: 'outcome',
  display: 'Outcome',
  system: 'http://hl7.org/fhir/search-entry-mode',
};
/**
 * Why an entry is in the result set - whether it's included as a match or because of an _include requirement, or to convey information or warning information about the search process.
 */
export const SearchEntryMode = {
  /**
   * This resource is returned because it is referred to from another resource in the search set.
   */
  Include: search_entry_mode_Include,
  /**
   * This resource matched the search specification.
   */
  Match: search_entry_mode_Match,
  /**
   * An OperationOutcome that provides additional information about the processing of a search.
   */
  Outcome: search_entry_mode_Outcome,
};
const search_modifier_code_Above: Coding = {
  code: 'above',
  display: 'Above',
  system: 'http://hl7.org/fhir/search-modifier-code',
};
const search_modifier_code_Below: Coding = {
  code: 'below',
  display: 'Below',
  system: 'http://hl7.org/fhir/search-modifier-code',
};
const search_modifier_code_Contains: Coding = {
  code: 'contains',
  display: 'Contains',
  system: 'http://hl7.org/fhir/search-modifier-code',
};
const search_modifier_code_Exact: Coding = {
  code: 'exact',
  display: 'Exact',
  system: 'http://hl7.org/fhir/search-modifier-code',
};
const search_modifier_code_Identifier: Coding = {
  code: 'identifier',
  display: 'Identifier',
  system: 'http://hl7.org/fhir/search-modifier-code',
};
const search_modifier_code_In: Coding = {
  code: 'in',
  display: 'In',
  system: 'http://hl7.org/fhir/search-modifier-code',
};
const search_modifier_code_Missing: Coding = {
  code: 'missing',
  display: 'Missing',
  system: 'http://hl7.org/fhir/search-modifier-code',
};
const search_modifier_code_Not: Coding = {
  code: 'not',
  display: 'Not',
  system: 'http://hl7.org/fhir/search-modifier-code',
};
const search_modifier_code_NotIn: Coding = {
  code: 'not-in',
  display: 'Not In',
  system: 'http://hl7.org/fhir/search-modifier-code',
};
const search_modifier_code_OfType: Coding = {
  code: 'ofType',
  display: 'Of Type',
  system: 'http://hl7.org/fhir/search-modifier-code',
};
const search_modifier_code_Text: Coding = {
  code: 'text',
  display: 'Text',
  system: 'http://hl7.org/fhir/search-modifier-code',
};
const search_modifier_code_Type: Coding = {
  code: 'type',
  display: 'Type',
  system: 'http://hl7.org/fhir/search-modifier-code',
};
/**
 * A supported modifier for a search parameter.
 */
export const SearchModifierCode = {
  /**
   * The search parameter tests whether the value in a resource subsumes the specified value (is-a, or hierarchical relationships).
   */
  Above: search_modifier_code_Above,
  /**
   * The search parameter tests whether the value in a resource is subsumed by the specified value (is-a, or hierarchical relationships).
   */
  Below: search_modifier_code_Below,
  /**
   * The search parameter returns resources that include the supplied parameter value anywhere within the field being searched.
   */
  Contains: search_modifier_code_Contains,
  /**
   * The search parameter returns resources that have a value that exactly matches the supplied parameter (the whole string, including casing and accents).
   */
  Exact: search_modifier_code_Exact,
  /**
   * The search parameter applies to the identifier on the resource, not the reference.
   */
  Identifier: search_modifier_code_Identifier,
  /**
   * The search parameter is a URI (relative or absolute) that identifies a value set, and the search parameter tests whether the coding is in the specified value set.
   */
  In: search_modifier_code_In,
  /**
   * The search parameter returns resources that have a value or not.
   */
  Missing: search_modifier_code_Missing,
  /**
   * The search parameter returns resources that do not contain a match.
   */
  Not: search_modifier_code_Not,
  /**
   * The search parameter is a URI (relative or absolute) that identifies a value set, and the search parameter tests whether the coding is not in the specified value set.
   */
  NotIn: search_modifier_code_NotIn,
  /**
   * The search parameter has the format system|code|value, where the system and code refer to an Identifier.type.coding.system and .code, and match if any of the type codes match. All 3 parts must be present.
   */
  OfType: search_modifier_code_OfType,
  /**
   * The search parameter is processed as a string that searches text associated with the code/value - either CodeableConcept.text, Coding.display, or Identifier.type.text.
   */
  Text: search_modifier_code_Text,
  /**
   * The search parameter only applies to the Resource Type specified as a modifier (e.g. the modifier is not actually :type, but :Patient etc.).
   */
  Type: search_modifier_code_Type,
};
const search_param_type_Composite: Coding = {
  code: 'composite',
  display: 'Composite',
  system: 'http://hl7.org/fhir/search-param-type',
};
const search_param_type_DateDateTime: Coding = {
  code: 'date',
  display: 'Date/DateTime',
  system: 'http://hl7.org/fhir/search-param-type',
};
const search_param_type_Number: Coding = {
  code: 'number',
  display: 'Number',
  system: 'http://hl7.org/fhir/search-param-type',
};
const search_param_type_Quantity: Coding = {
  code: 'quantity',
  display: 'Quantity',
  system: 'http://hl7.org/fhir/search-param-type',
};
const search_param_type_Reference: Coding = {
  code: 'reference',
  display: 'Reference',
  system: 'http://hl7.org/fhir/search-param-type',
};
const search_param_type_Special: Coding = {
  code: 'special',
  display: 'Special',
  system: 'http://hl7.org/fhir/search-param-type',
};
const search_param_type_String: Coding = {
  code: 'string',
  display: 'String',
  system: 'http://hl7.org/fhir/search-param-type',
};
const search_param_type_Token: Coding = {
  code: 'token',
  display: 'Token',
  system: 'http://hl7.org/fhir/search-param-type',
};
const search_param_type_URI: Coding = {
  code: 'uri',
  display: 'URI',
  system: 'http://hl7.org/fhir/search-param-type',
};
/**
 * Data types allowed to be used for search parameters.
 */
export const SearchParamType = {
  /**
   * A composite search parameter that combines a search on two values together.
   */
  Composite: search_param_type_Composite,
  /**
   * Search parameter is on a date/time. The date format is the standard XML format, though other formats may be supported.
   */
  DateDateTime: search_param_type_DateDateTime,
  /**
   * Search parameter SHALL be a number (a whole number, or a decimal).
   */
  Number: search_param_type_Number,
  /**
   * A search parameter that searches on a quantity.
   */
  Quantity: search_param_type_Quantity,
  /**
   * A reference to another resource (Reference or canonical).
   */
  Reference: search_param_type_Reference,
  /**
   * Special logic applies to this parameter per the description of the search parameter.
   */
  Special: search_param_type_Special,
  /**
   * Search parameter is a simple string, like a name part. Search is case-insensitive and accent-insensitive. May match just the start of a string. String parameters may contain spaces.
   */
  String: search_param_type_String,
  /**
   * Search parameter on a coded element or identifier. May be used to search through the text, display, code and code/codesystem (for codes) and label, system and key (for identifier). Its value is either a string or a pair of namespace and value, separated by a "|", depending on the modifier used.
   */
  Token: search_param_type_Token,
  /**
   * A search parameter that searches on a URI (RFC 3986).
   */
  URI: search_param_type_URI,
};
const search_xpath_usage_Distance: Coding = {
  code: 'distance',
  display: 'Distance',
  system: 'http://hl7.org/fhir/search-xpath-usage',
};
const search_xpath_usage_Nearby: Coding = {
  code: 'nearby',
  display: 'Nearby',
  system: 'http://hl7.org/fhir/search-xpath-usage',
};
const search_xpath_usage_Normal: Coding = {
  code: 'normal',
  display: 'Normal',
  system: 'http://hl7.org/fhir/search-xpath-usage',
};
const search_xpath_usage_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://hl7.org/fhir/search-xpath-usage',
};
const search_xpath_usage_Phonetic: Coding = {
  code: 'phonetic',
  display: 'Phonetic',
  system: 'http://hl7.org/fhir/search-xpath-usage',
};
/**
 * How a search parameter relates to the set of elements returned by evaluating its xpath query.
 */
export const SearchXpathUsage = {
  /**
   * The search parameter is based on a spatial transform of the selected nodes, using physical distance from the middle.
   */
  Distance: search_xpath_usage_Distance,
  /**
   * The search parameter is based on a spatial transform of the selected nodes.
   */
  Nearby: search_xpath_usage_Nearby,
  /**
   * The search parameter is derived directly from the selected nodes based on the type definitions.
   */
  Normal: search_xpath_usage_Normal,
  /**
   * The interpretation of the xpath statement is unknown (and can't be automated).
   */
  Other: search_xpath_usage_Other,
  /**
   * The search parameter is derived by a phonetic transform from the selected nodes.
   */
  Phonetic: search_xpath_usage_Phonetic,
};
const secondary_finding_ACMGVersion1: Coding = {
  code: 'acmg-version1',
  display: 'ACMG Version 1',
  system: 'http://hl7.org/fhir/secondary-finding',
};
const secondary_finding_ACMGVersion2: Coding = {
  code: 'acmg-version2',
  display: 'ACMG Version 2',
  system: 'http://hl7.org/fhir/secondary-finding',
};
/**
 * Codes to denote a guideline or policy statement.when a genetic test result is being shared as a secondary finding.
 */
export const SecondaryFinding = {
  /**
   * First release (2013): ACMG Recommendations for Reporting of Incidental Findings in Clinical Exome and Genome Sequencing.  https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3727274/
   */
  ACMGVersion1: secondary_finding_ACMGVersion1,
  /**
   * Second release (2016): Recommendations for reporting of secondary findings in clinical exome and genome sequencing, 2016 update (ACMG SF v2.0): a policy statement of the American College of Medical Genetics and Genomics. https://www.ncbi.nlm.nih.gov/pubmed/27854360
   */
  ACMGVersion2: secondary_finding_ACMGVersion2,
};
/**
 * This example FHIR value set is comprised of example Actor Type codes, which can be used to value FHIR agents, actors, and other role         elements such as those specified in financial transactions. The FHIR Actor value set is based on    DICOM Audit Message, C402;   ASTM Standard, E1762-95 [2013]; selected codes and          derived actor roles from HL7 RoleClass OID 2.16.840.1.113883.5.110;    HL7 Role Code 2.16.840.1.113883.5.111, including AgentRoleType;          HL7 ParticipationType OID: 2.16.840.1.113883.5.90; and    HL7 ParticipationFunction codes OID: 2.16.840.1.113883.5.88.           This value set includes, by reference, role codes from external code systems: NUCC Health Care Provider Taxonomy OID: 2.16.840.1.113883.6.101;          North American Industry Classification System [NAICS]OID: 2.16.840.1.113883.6.85; IndustryClassificationSystem 2.16.840.1.113883.1.11.16039;          and US Census Occupation Code OID: 2.16.840.1.113883.6.243 for relevant recipient or custodian codes not included in this value set.            If no source is indicated in the definition comments, then these are example FHIR codes.          It can be extended with appropriate roles described by SNOMED as well as those described in the HL7 Role Based Access Control Catalog and the          HL7 Healthcare (Security and Privacy) Access Control Catalog.            In Role-Based Access Control (RBAC), permissions are operations on an object that a user wishes to access. Permissions are grouped into roles.          A role characterizes the functions a user is allowed to perform. Roles are assigned to users. If the user's role has the appropriate permissions          to access an object, then that user is granted access to the object. FHIR readily enables RBAC, as FHIR Resources are object types and the CRUDE          events (the FHIR equivalent to permissions in the RBAC scheme) are operations on those objects.          In Attribute-Based Access Control (ABAC), a user requests to perform operations on objects. That user's access request is granted or denied          based on a set of access control policies that are specified in terms of attributes and conditions. FHIR readily enables ABAC, as instances of          a Resource in FHIR (again, Resources are object types) can have attributes associated with them. These attributes include security tags,          environment conditions, and a host of user and object characteristics, which are the same attributes as those used in ABAC. Attributes help          define the access control policies that determine the operations a user may perform on a Resource (in FHIR) or object (in ABAC). For example,          a tag (or attribute) may specify that the identified Resource (object) is not to be further disclosed without explicit consent from the patient.
 */
export const SecurityRoleType = {
  /**
   * A role type used to qualify a person's legal status within a country or nation.
   */
  CitizenRoleType_v3_RoleCode: v3_RoleCode_CitizenRoleType,
  /**
   * Audit participant role ID of software application
   */
  Application_dicom_dcim: dicom_dcim_Application,
  /**
   * Audit participant role ID of software application launcher, i.e., the entity that started or stopped an application
   */
  ApplicationLauncher_dicom_dcim: dicom_dcim_ApplicationLauncher,
  /**
   * Audit participant role ID of the receiver of data
   */
  DestinationRoleID_dicom_dcim: dicom_dcim_DestinationRoleID,
  /**
   * Audit participant role ID of the sender of data
   */
  SourceRoleID_dicom_dcim: dicom_dcim_SourceRoleID,
  /**
   * Audit participant role ID of media receiving data during an export
   */
  DestinationMedia_dicom_dcim: dicom_dcim_DestinationMedia,
  /**
   * Audit participant role ID of media providing data during an import
   */
  SourceMedia_dicom_dcim: dicom_dcim_SourceMedia,
  /**
   * Player of the Affiliate role has a business/professional relationship with scoper.  Player and scoper may be persons or organization.  The Affiliate relationship does not imply membership in a group, nor does it exist for resource scheduling purposes.
   *
   *
   *                            Example: A healthcare provider is affiliated with another provider as a business associate.
   */
  Affiliate_v3_RoleClass: v3_RoleClass_Affiliate,
  /**
   * An entity (player) that acts or is authorized to act on behalf of another entity (scoper).
   */
  Agent_v3_RoleClass: v3_RoleClass_Agent,
  /**
   * A person who has corrected, edited, or amended pre-existing information.
   */
  Amender_contract_signer_type: contract_signer_type_Amender,
  /**
   * An agent role in which the agent is an Entity acting in the employ of an organization.  The focus is on functional role on behalf of the organization, unlike the Employee role where the focus is on the 'Human Resources' relationship between the employee and the organization.
   */
  AssignedEntity_v3_RoleClass: v3_RoleClass_AssignedEntity,
  /**
   * Description:Caregiver authorized to receive patient health information.
   */
  CaregiverInformationReceiver_v3_ParticipationFunction: v3_ParticipationFunction_CaregiverInformationReceiver,
  /**
   * Description:Provider with legitimate relationship authorized to receive patient health information.
   */
  LegitimateRelationshipInformationReceiver_v3_ParticipationFunction: v3_ParticipationFunction_LegitimateRelationshipInformationReceiver,
  /**
   * Definition: A party that originates the Act and therefore has responsibility for the information given in the Act and ownership of this Act.
   *
   *
   *                            Example: the report writer, the person writing the act definition, the guideline author, the placer of an order, the EKG cart (device) creating a report etc. Every Act should have an author. Authorship is regardless of mood always actual authorship.
   *
   *                         Examples of such policies might include:
   *
   *
   *
   *                               The author and anyone they explicitly delegate may update the report;
   *
   *
   *
   *                               All administrators within the same clinic may cancel and reschedule appointments created by other administrators within that clinic;
   *
   *
   *
   *                         A party that is neither an author nor a party who is extended authorship maintenance rights by policy, may only amend, reverse, override, replace, or follow up in other ways on this Act, whereby the Act remains intact and is linked to another Act authored by that other party.
   */
  AuthorOriginator_v3_ParticipationType: v3_ParticipationType_AuthorOriginator,
  /**
   * An entity providing authorization services to enable the electronic sharing of health-related information based on resource owner's preapproved permissions. For example, an UMA Authorization Server[UMA]
   */
  AuthorizationServer_extra_security_role_type: extra_security_role_type_AuthorizationServer,
  /**
   * Description:Member of care team authorized to receive patient health information.
   */
  CareTeamInformationReceiver_v3_ParticipationFunction: v3_ParticipationFunction_CareTeamInformationReceiver,
  /**
   * Description:Entities within specified work area authorized to receive patient health information.
   */
  WorkAreaInformationReceiver_v3_ParticipationFunction: v3_ParticipationFunction_WorkAreaInformationReceiver,
  /**
   * A person who has fled his or her home country to find a safe place elsewhere.
   */
  AsylumSeeker_v3_RoleCode: v3_RoleCode_AsylumSeeker,
  /**
   * A person who is someone of below legal age who has fled his or her home country, without his or her parents, to find a safe place elsewhere at time of categorization.
   */
  SingleMinorAsylumSeeker_v3_RoleCode: v3_RoleCode_SingleMinorAsylumSeeker,
  /**
   * Description: A role played by a party making a claim for coverage under a policy or program.  A claimant must be either a person or organization, or a group of persons or organizations.  A claimant is not a named insured or a program eligible.
   *
   *
   *                            Discussion: With respect to liability insurance such as property and casualty insurance, a claimant must file a claim requesting indemnification for a loss that the claimant considers covered under the policy of a named insured.  The claims adjuster for the policy underwriter will review the claim to determine whether the loss meets the benefit coverage criteria under a policy, and base any indemnification or coverage payment on that review.  If a third party is liable in whole or part for the loss, the underwriter may pursue third party liability recovery.  A claimant may be involved in civil or criminal legal proceedings involving claims against a defendant party that is indemnified by an insurance policy or to protest the finding of a claims adjustor. With respect to life insurance, a beneficiary designated by a named insured becomes a claimant of the proceeds of coverage, as in the case of a life insurance policy.  However, a claimant for coverage under life insurance is not necessarily a designated beneficiary.
   *
   *
   *                            Note: A claimant is not a named insured.  However, a named insured may make a claim under a policy, e.g., an insured driver may make a claim for an injury under his or her comprehensive automobile insurance policy.  Similarly, a program eligible may make a claim under program, e.g., an unemployed worker may claim benefits under an unemployment insurance program, but parties playing these covered party role classes are not, for purposes of this vocabulary and in an effort to clearly distinguish role classes, considered claimants.
   *
   *                         In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that either a named insured or an individual insured has filed a claim for a loss.  In the case of a program eligible, a role type code INJWKR (injured worker) subtypes the class to indicate that the covered party in a workers compensation program is an injured worker, and as such, has filed a "claim" under the program for benefits.  Likewise, a covered role type code UNEMP (unemployed worker) subtypes the program eligible class to indicate that the covered party in an unemployment insurance program has filed a claim for unemployment benefits.
   *
   *
   *                            Example: A claimant under automobile policy that is not the named insured.
   */
  Claimant_v3_RoleClass: v3_RoleClass_Claimant,
  /**
   * An individual authorized to assign an original classification to information, including compilations of unclassified information, based on a determination that the information requires protection against unauthorized disclosure. The individual marks the information with immutable, computable, and human readable security labels in accordance with applicable security labeling policies.  The labeling policies provide instructions on whether and if so how the security labels may be later reclassified [i.e., upgraded, downgraded, used in derivative classification, or declassified] in a manner that preserves the overridden original classification binding and provenance.
   */
  Classifier_v3_RoleCode: v3_RoleCode_Classifier,
  /**
   * A person who is legally recognized as a member of a nation or country, with associated rights and obligations.
   */
  National_v3_RoleCode: v3_RoleCode_National,
  /**
   * A foreigner who is present in a country (which is foreign to him/her) unlawfully or without the country's authorization (may be called an illegal alien).
   */
  NonCountryMemberWithoutResidencePermit_v3_RoleCode: v3_RoleCode_NonCountryMemberWithoutResidencePermit,
  /**
   * A person who is below legal age present in a country, without his or her parents, (which is foreign to him/her) unlawfully or without the country's authorization.
   */
  NonCountryMemberMinorWithoutResidencePermit_v3_RoleCode: v3_RoleCode_NonCountryMemberMinorWithoutResidencePermit,
  /**
   * The entity that co-authored content. There can be multiple co-authors of content, which may take such forms as a contract, a healthcare record entry or document, a policy, or a consent directive.
   */
  CoAuthor_contract_signer_type: contract_signer_type_CoAuthor,
  /**
   * An entity or an entity's delegatee who is the grantee in an agreement such as a consent for services, advanced directive, or a privacy consent directive in accordance with jurisdictional, organizational, or patient policy.
   */
  Consenter_v3_RoleCode: v3_RoleCode_Consenter,
  /**
   * An entity which has witnessed and attests to observing another entity being counseled about an agreement such as a consent for services, advanced directive, or a privacy consent directive.
   */
  ConsentWitness_v3_RoleCode: v3_RoleCode_ConsentWitness,
  /**
   * A person or an organization that provides or receives information regarding another entity. Examples; patient NOK and emergency contacts; guarantor contact; employer contact.
   */
  Contact_contract_signer_type: contract_signer_type_Contact,
  /**
   * An entity which participates in the generation of and attest to veracity of content, but is not an author or coauthor. For example a surgeon who is required by institutional, regulatory, or legal rules to sign an operative report, but who was not involved in the authorship of that report.
   */
  CoParticipant_v3_RoleCode: v3_RoleCode_CoParticipant,
  /**
   * A role class played by a person who receives benefit coverage under the terms of a particular insurance policy.  The underwriter of that policy is the scoping entity.  The covered party receives coverage because of some contractual or other relationship with the holder of that policy.
   *
   *
   *                            Discussion:This reason for coverage is captured in 'Role.code' and a relationship link with type code of indirect authority should be included using the policy holder role as the source, and the covered party role as the target.
   *
   *                         Note that a particular policy may cover several individuals one of whom may be, but need not be, the policy holder.  Thus the notion of covered party is a role that is distinct from that of the policy holder.
   */
  CoveredParty_v3_RoleClass: v3_RoleClass_CoveredParty,
  /**
   * A non-country member admitted to the territory of a nation or country as a non-resident for an explicit purpose.
   */
  PermitCardApplicant_v3_RoleCode: v3_RoleCode_PermitCardApplicant,
  /**
   * A foreigner who is a resident of the country but does not have citizenship.
   */
  NonCountryMemberWithResidencePermit_v3_RoleCode: v3_RoleCode_NonCountryMemberWithResidencePermit,
  /**
   * A person who is a resident below legal age of the country without his or her parents and does not have citizenship.
   */
  NonCountryMemberMinorWithResidencePermit_v3_RoleCode: v3_RoleCode_NonCountryMemberMinorWithResidencePermit,
  /**
   * An entity (person, organization or device) that is in charge of maintaining the information of this act (e.g., who maintains the report or the master service catalog item, etc.).
   */
  Custodian_v3_ParticipationType: v3_ParticipationType_Custodian,
  /**
   * An entity that collects information over which the data subject may have certain rights under policy or law to control that information's management and distribution by data collectors, including the right to access, retrieve, distribute, or delete that information.
   */
  DataCollector_extra_security_role_type: extra_security_role_type_DataCollector,
  /**
   * An entity that processes collected information over which the data subject may have certain rights under policy or law to control that information's management and distribution by data processors, including the right to access, retrieve, distribute, or delete that information.
   */
  DataProcessor_extra_security_role_type: extra_security_role_type_DataProcessor,
  /**
   * A person whose personal information is collected or processed, and who may have certain rights under policy or law to control that information's management and distribution by data collectors or processors, including the right to access, retrieve, distribute, or delete that information.
   */
  DataSubject_extra_security_role_type: extra_security_role_type_DataSubject,
  /**
   * An individual which is authorized to declassify information based on a determination that the information no longer requires protection against unauthorized disclosure.  The individual marks the information being declassified using computable and human readable security labels indicating that this is copy of previously classified information is unclassified in accordance with applicable security labeling policies.  The labeling policies provide instructions on whether and if so how the security labels may be later reclassified [i.e., upgraded or used in derivative classification] in a manner that preserves the overridden original classification binding and provenance.
   */
  Declassifier_v3_RoleCode: v3_RoleCode_Declassifier,
  /**
   * A party to whom some right or authority is granted by a delegator.
   */
  Delegatee_v3_RoleCode: v3_RoleCode_Delegatee,
  /**
   * A party that grants all or some portion its right or authority to another party.
   */
  Delegator_v3_RoleCode: v3_RoleCode_Delegator,
  /**
   * Description: A role played by a person covered under a policy or program based on an association with a subscriber, which is recognized by the policy holder.
   *
   *
   *                            Note:  The party playing the role of a dependent is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a dependent may make a claim under a policy, e.g., a dependent under a health insurance policy may become the claimant for coverage under that policy for wellness examines or if injured and there is no liable third party.  In the case of a dependent making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that the dependent has filed a claim for services covered under the health insurance policy.
   *
   *
   *                            Example: The dependent has an association with the subscriber such as a financial dependency or personal relationship such as that of a spouse, or a natural or adopted child.  The policy holder may be required by law to recognize certain associations or may have discretion about the associations.  For example, a policy holder may dictate the criteria for the dependent status of adult children who are students, such as requiring full time enrollment, or may recognize domestic partners as dependents.  Under certain circumstances, the dependent may be under the indirect authority of a responsible party acting as a surrogate for the subscriber, for example, if the subscriber is differently abled or deceased, a guardian ad Lidem or estate executor may be appointed to assume the subscriberaTMs legal standing in the relationship with the dependent.
   */
  Dependent_v3_RoleClass: v3_RoleClass_Dependent,
  /**
   * An individual authorized to lower the classification level of labeled content and provide rationale for doing so as directed by a classification guide.
   */
  Downgrader_v3_RoleCode: v3_RoleCode_Downgrader,
  /**
   * A relationship between two people in which one person authorizes another, usually a family member or relative, to act for him or her in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts that is often limited in the kinds of powers that can be assigned.  Unlike ordinary powers of attorney, durable powers can survive for long periods of time, and again, unlike standard powers of attorney, durable powers can continue after incompetency.
   */
  DurablePowerOfAttorney_v3_RoleCode: v3_RoleCode_DurablePowerOfAttorney,
  /**
   * An entity to be contacted in the event of an emergency.
   */
  EmergencyContact_v3_RoleClass: v3_RoleClass_EmergencyContact,
  /**
   * A relationship between a person or organization and a person or organization formed for the purpose of exchanging work for compensation.  The purpose of the role is to identify the type of relationship the employee has to the employer, rather than the nature of the work actually performed.  (Contrast with AssignedEntity.)
   */
  Employee_v3_RoleClass: v3_RoleClass_Employee,
  /**
   * A person who attests to observing an occurrence.  For example, the witness has observed a procedure and is attesting to this fact.
   */
  EventWitness_contract_signer_type: contract_signer_type_EventWitness,
  /**
   * The role played by a person acting as the estate executor for a deceased subscriber or policyholder who was the responsible party
   */
  ExecutorOfEstate_v3_RoleCode: v3_RoleCode_ExecutorOfEstate,
  /**
   * An entity which accepts certain rights or authority from a grantor.
   */
  Grantee_v3_RoleCode: v3_RoleCode_Grantee,
  /**
   * An entity which agrees to confer certain rights or authority to a grantee.
   */
  Grantor_v3_RoleCode: v3_RoleCode_Grantor,
  /**
   * An individual or organization that makes or gives a promise, assurance, pledge to pay or has paid the healthcare service provider.
   */
  Guarantor_v3_RoleCode: v3_RoleCode_Guarantor,
  /**
   * The role played by a person appointed by the court to look out for the best interests of a minor child during the course of legal proceedings.
   */
  GuardianAdLidem_v3_RoleCode: v3_RoleCode_GuardianAdLidem,
  /**
   * Guardian of a ward
   */
  Guardian_v3_RoleClass: v3_RoleClass_Guardian,
  /**
   * A relationship between two people in which one person authorizes another to act for him or her in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts that continues (by its terms) to be effective even though the grantor has become mentally incompetent after signing the document.
   */
  HealthcarePowerOfAttorney_v3_RoleCode: v3_RoleCode_HealthcarePowerOfAttorney,
  /**
   * The human user that has participated.
   */
  HumanUser_extra_security_role_type: extra_security_role_type_HumanUser,
  /**
   * A source of reported information (e.g., a next of kin who answers questions about the patient's history).  For history questions, the patient is logically an informant, yet the informant of history questions is implicitly the subject.
   */
  Informant_v3_ParticipationType: v3_ParticipationType_Informant,
  /**
   * An entity which converts spoken or written language into the language of key participants in an event such as when a provider is obtaining a patient's consent to treatment or permission to disclose information.
   */
  Interpreter_v3_RoleCode: v3_RoleCode_Interpreter,
  /**
   * An entity that is the subject of an investigation. This role is scoped by the party responsible for the investigation.
   */
  InvestigationSubject_v3_RoleClass: v3_RoleClass_InvestigationSubject,
  /**
   * A party, who may or should receive or who has recieved the Act or subsequent or derivative information of that Act. Information recipient is inert, i.e., independent of mood." Rationale: this is a generalization of a too diverse family that the definition can't be any more specific, and the concept is abstract so one of the specializations should be used.
   */
  InformationRecipient_v3_ParticipationType: v3_ParticipationType_InformationRecipient,
  /**
   * A verifier who legally authenticates the accuracy of an act. An example would be a staff physician who sees a patient and dictates a note, then later signs it. Their signature constitutes a legal authentication.
   */
  LegalAuthenticator_v3_ParticipationType: v3_ParticipationType_LegalAuthenticator,
  /**
   * Description: A role played by a party to an insurance policy to which the insurer agrees to indemnify for losses, provides benefits for, or renders services.  A named insured may be either a person, non-person living subject, or an organization, or a group of persons, non-person living subjects, or organizations.
   *
   *
   *                            Discussion: The coded concept NAMED should not be used where a more specific child concept in this Specializable value set applies.  In some cases, the named insured may not be the policy holder, e.g., where a policy holder purchases life insurance policy in which another party is the named insured and the policy holder is the beneficiary of the policy.
   *
   *
   *                            Note: The party playing the role of a named insured is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a named insured may make a claim under a policy, e.g., e.g., a party that is the named insured and policy holder under a comprehensive automobile insurance policy may become the claimant for coverage under that policy e.g., if injured in an automobile accident and there is no liable third party.  In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that a named insured has filed a claim for a loss.
   *
   *
   *                            Example: The named insured under a comprehensive automobile, disability, or property and casualty policy that is the named insured and may or may not be the policy holder.
   */
  NamedInsured_v3_RoleClass: v3_RoleClass_NamedInsured,
  /**
   * An individual designated for notification as the next of kin for a given entity.
   */
  NextOfKin_v3_RoleClass: v3_RoleClass_NextOfKin,
  /**
   * notary public
   */
  NotaryPublic_v3_RoleClass: v3_RoleClass_NotaryPublic,
  /**
   * A Role of a LivingSubject (player) as an actual or potential recipient of health care services from a healthcare provider organization (scoper).
   *
   *
   *                            Usage Note: Communication about relationships between patients and specific healthcare practitioners (people) is not done via scoper.  Instead this is generally done using the CareProvision act.  This allows linkage between patient and a particular healthcare practitioner role and also allows description of the type of care involved in the relationship.
   */
  Patient_v3_RoleClass: v3_RoleClass_Patient,
  /**
   * A relationship between two people in which one person authorizes another to act for him in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts.
   */
  PowerOfAttorney_v3_RoleCode: v3_RoleCode_PowerOfAttorney,
  /**
   * An entity that is the primary or sole author of information content.  In the healthcare context, there can be only one primary author of health information content in a record entry or document.
   */
  PrimaryAuthor_contract_signer_type: contract_signer_type_PrimaryAuthor,
  /**
   * Definition:Provider authorized to mask information to protect the patient, a third party, or to ensure that the provider has consulted with the patient prior to release of this information.
   */
  AuthorizedProviderMaskingAuthor_v3_ParticipationFunction: v3_ParticipationFunction_AuthorizedProviderMaskingAuthor,
  /**
   * An Entity (player) that is authorized to provide health care services by some authorizing agency (scoper).
   */
  HealthcareProvider_v3_RoleClass: v3_RoleClass_HealthcareProvider,
  /**
   * The role played by a party who has legal responsibility for another party.
   */
  ResponsibleParty_v3_RoleCode: v3_RoleCode_ResponsibleParty,
  /**
   * A person, device, or algorithm that has used approved criteria for filtered data for inclusion into the patient record.  Examples: (1) a medical records clerk who scans a document for inclusion in the medical record, enters header information, or catalogues and classifies the data, or a combination thereof; (2) a gateway that receives data from another computer system and interprets that data or changes its format, or both, before entering it into the patient record.
   */
  Reviewer_contract_signer_type: contract_signer_type_Reviewer,
  /**
   * An automated data source that generates a signature along with content. Examples: (1) the signature for an image that is generated by a device for inclusion in the patient record; (2) the signature for an ECG derived by an ECG system for inclusion in the patient record; (3) the data from a biomedical monitoring device or system that is for inclusion in the patient record.
   */
  Source_contract_signer_type: contract_signer_type_Source,
  /**
   * A relationship between two people in which one person authorizes another to act for him or her in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts that is often limited in the kinds of powers that can be assigned.
   */
  SpecialPowerOfAttorney_v3_RoleCode: v3_RoleCode_SpecialPowerOfAttorney,
  /**
   * An entity entering the data into the originating system. This includes the transcriptionist for dictated text transcribed into electronic form.
   */
  Transcriber_contract_signer_type: contract_signer_type_Transcriber,
  /**
   * A secondary information recipient, who receives copies (e.g., a primary care provider receiving copies of results as ordered by specialist).
   */
  Tracker_v3_ParticipationType: v3_ParticipationType_Tracker,
  /**
   * A person who validates a health information document for inclusion in the patient record. For example, a medical student or resident is credentialed to perform history or physical examinations and to write progress notes. The attending physician signs the history and physical examination to validate the entry for inclusion in the patient's medical record.
   */
  Validator_contract_signer_type: contract_signer_type_Validator,
  /**
   * A person who asserts the correctness and appropriateness of an act or the recording of the act, and is accountable for the assertion that the act or the recording of the act complies with jurisdictional or organizational policy. For example, a physician is required to countersign a verbal order that has previously been recorded in the medical record by a registered nurse who has carried out the verbal order.
   */
  Verifier_contract_signer_type: contract_signer_type_Verifier,
  /**
   * Only with service events.  A person witnessing the action happening without doing anything.  A witness is not necessarily aware, much less approves of anything stated in the service event.  Example for a witness is students watching an operation or an advanced directive witness.
   */
  Witness_v3_ParticipationType: v3_ParticipationType_Witness,
};
const sequence_type_AASequence: Coding = {
  code: 'aa',
  display: 'AA Sequence',
  system: 'http://hl7.org/fhir/sequence-type',
};
const sequence_type_DNASequence: Coding = {
  code: 'dna',
  display: 'DNA Sequence',
  system: 'http://hl7.org/fhir/sequence-type',
};
const sequence_type_RNASequence: Coding = {
  code: 'rna',
  display: 'RNA Sequence',
  system: 'http://hl7.org/fhir/sequence-type',
};
/**
 * Type if a sequence -- DNA, RNA, or amino acid sequence.
 */
export const SequenceType = {
  /**
   * Amino acid sequence.
   */
  AASequence: sequence_type_AASequence,
  /**
   * DNA Sequence.
   */
  DNASequence: sequence_type_DNASequence,
  /**
   * RNA Sequence.
   */
  RNASequence: sequence_type_RNASequence,
};
/**
 * Performer function of an agent in an imaging study series
 */
export const SeriesPerformerFunction = {
  /**
   * An advisor participating in the service by performing evaluations and making recommendations.
   */
  Consultant: v3_ParticipationType_Consultant,
  /**
   * Definition: A person, non-person living subject, organization or device that who actually and principally carries out the action. Device should only be assigned as a performer in circumstances where the device is performing independent of human intervention.  Need not be the principal responsible actor.
   *
   *
   *                            Exampe: A surgery resident operating under supervision of attending surgeon, a search and rescue dog locating survivors, an electronic laboratory analyzer or the laboratory discipline requested to perform a laboratory test. The performer may also be the patient in self-care, e.g. fingerstick blood sugar. The traditional order filler is a performer. This information should accompany every service event.
   *
   *
   *                            Note: that existing HL7 designs assign an organization as the playing entity of the Role that is the performer.  These designs should be revised in subsequent releases to make this the scooping entity for the role involved.
   */
  Performer: v3_ParticipationType_Performer,
  /**
   * A person having referred the subject of the service to the performer (referring physician).  Typically, a referring physician will receive a report.
   */
  Referrer: v3_ParticipationType_Referrer,
  /**
   * A person assisting in an act through his substantial presence and involvement   This includes: assistants, technicians, associates, or whatever the job titles may be.
   */
  SecondaryPerformer: v3_ParticipationType_SecondaryPerformer,
  /**
   * A person who verifies the correctness and appropriateness of the service (plan, order, event, etc.) and hence takes on accountability.
   */
  Verifier: v3_ParticipationType_Verifier,
};
const service_category_Adoption: Coding = {
  code: '1',
  display: 'Adoption',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_Dental: Coding = {
  code: '10',
  display: 'Dental',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_DisabilitySupport: Coding = {
  code: '11',
  display: 'Disability Support',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_DrugAlcohol: Coding = {
  code: '12',
  display: 'Drug/Alcohol',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_EducationAndLearning: Coding = {
  code: '13',
  display: 'Education & Learning',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_EmergencyDepartment: Coding = {
  code: '14',
  display: 'Emergency Department',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_Employment: Coding = {
  code: '15',
  display: 'Employment',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_FinancialAndMaterialAid: Coding = {
  code: '16',
  display: 'Financial & Material Aid',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_GeneralPractice: Coding = {
  code: '17',
  display: 'General Practice',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_HousingHomelessness: Coding = {
  code: '18',
  display: 'Housing/Homelessness',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_Interpreting: Coding = {
  code: '19',
  display: 'Interpreting',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_AgedCare: Coding = {
  code: '2',
  display: 'Aged Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_Justice: Coding = {
  code: '20',
  display: 'Justice',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_Legal: Coding = {
  code: '21',
  display: 'Legal',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_MentalHealth: Coding = {
  code: '22',
  display: 'Mental Health',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_PhysicalActivityAndRecreation: Coding = {
  code: '23',
  display: 'Physical Activity & Recreation',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_Regulation: Coding = {
  code: '24',
  display: 'Regulation',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_RespiteCarerSupport: Coding = {
  code: '25',
  display: 'Respite/Carer Support',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_SpecialistClinicalPathology: Coding = {
  code: '26',
  display: 'Specialist Clinical Pathology',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_SpecialistMedical: Coding = {
  code: '27',
  display: 'Specialist Medical',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_SpecialistObstetricsAndGynecology: Coding = {
  code: '28',
  display: 'Specialist Obstetrics & Gynecology',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_SpecialistPaediatric: Coding = {
  code: '29',
  display: 'Specialist Paediatric',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_AlternativeComplementaryTherapies: Coding = {
  code: '3',
  display: 'Alternative/Complementary Therapies',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_SpecialistRadiologyImaging: Coding = {
  code: '30',
  display: 'Specialist Radiology/Imaging',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_SpecialistSurgical: Coding = {
  code: '31',
  display: 'Specialist Surgical',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_SupportGroupS: Coding = {
  code: '32',
  display: 'Support Group/s',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_Transport: Coding = {
  code: '33',
  display: 'Transport',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_AlliedHealth: Coding = {
  code: '34',
  display: 'Allied Health',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_Hospital: Coding = {
  code: '35',
  display: 'Hospital',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_CrisisLineGPAHUseOnly: Coding = {
  code: '36',
  display: 'Crisis Line (GPAH use only)',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_TestMessageHSDAdmin: Coding = {
  code: '37',
  display: 'Test Message (HSD admin)',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_NDIA: Coding = {
  code: '38',
  display: 'NDIA',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_ChildCareKindergarten: Coding = {
  code: '4',
  display: 'Child Care /Kindergarten',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_ChildDevelopment: Coding = {
  code: '5',
  display: 'Child Development',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_ChildProtectionAndFamilyServices: Coding = {
  code: '6',
  display: 'Child Protection & Family Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_CommunityHealthCare: Coding = {
  code: '7',
  display: 'Community Health Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_Counselling: Coding = {
  code: '8',
  display: 'Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
const service_category_DeathServices: Coding = {
  code: '9',
  display: 'Death Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-category',
};
/**
 * This value set defines an example set of codes that can be used to classify groupings of service-types/specialties.
 */
export const ServiceCategory = {
  /**
   * Adoption
   */
  Adoption: service_category_Adoption,
  /**
   * Dental
   */
  Dental: service_category_Dental,
  /**
   * Disability Support
   */
  DisabilitySupport: service_category_DisabilitySupport,
  /**
   * Drug/Alcohol
   */
  DrugAlcohol: service_category_DrugAlcohol,
  /**
   * Education & Learning
   */
  EducationAndLearning: service_category_EducationAndLearning,
  /**
   * Emergency Department
   */
  EmergencyDepartment: service_category_EmergencyDepartment,
  /**
   * Employment
   */
  Employment: service_category_Employment,
  /**
   * Financial & Material aid
   */
  FinancialAndMaterialAid: service_category_FinancialAndMaterialAid,
  /**
   * General Practice/GP (doctor)
   */
  GeneralPractice: service_category_GeneralPractice,
  /**
   * Housing/Homelessness
   */
  HousingHomelessness: service_category_HousingHomelessness,
  /**
   * Interpreting
   */
  Interpreting: service_category_Interpreting,
  /**
   * Aged Care
   */
  AgedCare: service_category_AgedCare,
  /**
   * Justice
   */
  Justice: service_category_Justice,
  /**
   * Legal
   */
  Legal: service_category_Legal,
  /**
   * Mental Health
   */
  MentalHealth: service_category_MentalHealth,
  /**
   * Physical Activity & Recreation
   */
  PhysicalActivityAndRecreation: service_category_PhysicalActivityAndRecreation,
  /**
   * Regulation
   */
  Regulation: service_category_Regulation,
  /**
   * Respite/Carer Support
   */
  RespiteCarerSupport: service_category_RespiteCarerSupport,
  /**
   * Specialist Clinical Pathology - requires referral
   */
  SpecialistClinicalPathology: service_category_SpecialistClinicalPathology,
  /**
   * Specialist Medical - requires referral
   */
  SpecialistMedical: service_category_SpecialistMedical,
  /**
   * Specialist Obstetrics & Gynecology - requires referral
   */
  SpecialistObstetricsAndGynecology: service_category_SpecialistObstetricsAndGynecology,
  /**
   * Specialist Paediatric - requires referral
   */
  SpecialistPaediatric: service_category_SpecialistPaediatric,
  /**
   * Alternative & Complementary Therapies
   */
  AlternativeComplementaryTherapies: service_category_AlternativeComplementaryTherapies,
  /**
   * Specialist Radiology/Imaging - requires referral
   */
  SpecialistRadiologyImaging: service_category_SpecialistRadiologyImaging,
  /**
   * Specialist Surgical - requires referral
   */
  SpecialistSurgical: service_category_SpecialistSurgical,
  /**
   * Support group/s
   */
  SupportGroupS: service_category_SupportGroupS,
  /**
   * Transport
   */
  Transport: service_category_Transport,
  /**
   * Allied Health
   */
  AlliedHealth: service_category_AlliedHealth,
  /**
   * Hospital
   */
  Hospital: service_category_Hospital,
  /**
   * Crisis Line (GPAH use only)
   */
  CrisisLineGPAHUseOnly: service_category_CrisisLineGPAHUseOnly,
  /**
   * Test Message (HSD admin use only)
   */
  TestMessageHSDAdmin: service_category_TestMessageHSDAdmin,
  /**
   * NDIA
   */
  NDIA: service_category_NDIA,
  /**
   * Child Care and/or Kindergarten
   */
  ChildCareKindergarten: service_category_ChildCareKindergarten,
  /**
   * Child Development
   */
  ChildDevelopment: service_category_ChildDevelopment,
  /**
   * Child Protection & Family Services
   */
  ChildProtectionAndFamilyServices: service_category_ChildProtectionAndFamilyServices,
  /**
   * Community Health Care
   */
  CommunityHealthCare: service_category_CommunityHealthCare,
  /**
   * Counselling
   */
  Counselling: service_category_Counselling,
  /**
   * Death Services
   */
  DeathServices: service_category_DeathServices,
};
const service_modifiers_AfterHours: Coding = {
  code: 'ah',
  display: 'After hours',
  system: 'http://hl7.org/fhir/ex-servicemodifier',
};
const service_modifiers_SideOfTheRoad: Coding = {
  code: 'sr',
  display: 'Side of the Road',
  system: 'http://hl7.org/fhir/ex-servicemodifier',
};
/**
 * This value set includes sample Service Modifier codes.
 */
export const ServiceModifiers = {
  /**
   * Services provided outside or normal business hours.
   */
  AfterHours: service_modifiers_AfterHours,
  /**
   * Services provided on the side of the road or such other non-conventional setting.
   */
  SideOfTheRoad: service_modifiers_SideOfTheRoad,
};
const service_pharmacy_CompoundingFee: Coding = {
  code: 'compoundfee',
  display: 'Compounding Fee',
  system: 'http://hl7.org/fhir/ex-pharmaservice',
};
const service_pharmacy_DispenseFee: Coding = {
  code: 'dispensefee',
  display: 'Dispense Fee',
  system: 'http://hl7.org/fhir/ex-pharmaservice',
};
const service_pharmacy_DrugCost: Coding = {
  code: 'drugcost',
  display: 'Drug Cost',
  system: 'http://hl7.org/fhir/ex-pharmaservice',
};
const service_pharmacy_FluShot: Coding = {
  code: 'flushot',
  display: 'Flu Shot',
  system: 'http://hl7.org/fhir/ex-pharmaservice',
};
const service_pharmacy_Markup: Coding = {
  code: 'markup',
  display: 'Markup',
  system: 'http://hl7.org/fhir/ex-pharmaservice',
};
const service_pharmacy_SmokingCessation: Coding = {
  code: 'smokecess',
  display: 'Smoking cessation',
  system: 'http://hl7.org/fhir/ex-pharmaservice',
};
/**
 * This value set includes a smattering of Pharmacy Service codes.
 */
export const ServicePharmacy = {
  /**
   * The professional fee charged for compounding the medication.
   */
  CompoundingFee: service_pharmacy_CompoundingFee,
  /**
   * The professional fee charged for dispensing the product or service.
   */
  DispenseFee: service_pharmacy_DispenseFee,
  /**
   * The wholesale price of the medication.
   */
  DrugCost: service_pharmacy_DrugCost,
  /**
   * Flu Shot
   */
  FluShot: service_pharmacy_FluShot,
  /**
   * The additional cost assessed on the drug.
   */
  Markup: service_pharmacy_Markup,
  /**
   * Smoking cessation
   */
  SmokingCessation: service_pharmacy_SmokingCessation,
};
const service_place_Pharmacy: Coding = {
  code: '01',
  display: 'Pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_School: Coding = {
  code: '03',
  display: 'School',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_HomelessShelter: Coding = {
  code: '04',
  display: 'Homeless Shelter',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_IndianHealthServiceFreeStandingFacility: Coding = {
  code: '05',
  display: 'Indian Health Service Free-standing Facility',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_IndianHealthServiceProviderBasedFacility: Coding = {
  code: '06',
  display: 'Indian Health Service Provider-based Facility',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_Tribal638FreeStandingFacility: Coding = {
  code: '07',
  display: 'Tribal 638 Free-Standing Facility',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_Tribal638ProviderBasedFacility: Coding = {
  code: '08',
  display: 'Tribal 638 Provider-Based Facility',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_PrisonCorrectionalFacility: Coding = {
  code: '09',
  display: 'Prison/Correctional Facility',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_Office: Coding = {
  code: '11',
  display: 'Office',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_Home: Coding = {
  code: '12',
  display: 'Home',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_AssistedLivingFa: Coding = {
  code: '13',
  display: 'Assisted Living Fa',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_GroupHome: Coding = {
  code: '14',
  display: 'Group Home',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_MobileUnit: Coding = {
  code: '15',
  display: 'Mobile Unit',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_OffCampusOutpatientHospital: Coding = {
  code: '19',
  display: 'Off Campus-Outpatient Hospital',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_UrgentCareFacility: Coding = {
  code: '20',
  display: 'Urgent Care Facility',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_InpatientHospital: Coding = {
  code: '21',
  display: 'Inpatient Hospital',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
const service_place_AmbulanceLand: Coding = {
  code: '41',
  display: 'Ambulance—Land',
  system: 'http://terminology.hl7.org/CodeSystem/ex-serviceplace',
};
/**
 * This value set includes a smattering of Service Place codes.
 */
export const ServicePlace = {
  /**
   * A facility or location where drugs and other medically related items and services are sold, dispensed, or otherwise provided directly to patients.
   */
  Pharmacy: service_place_Pharmacy,
  /**
   * A facility whose primary purpose is education.
   */
  School: service_place_School,
  /**
   * A facility or location whose primary purpose is to provide temporary housing to homeless individuals (e.g., emergency shelters, individual or family shelters).
   */
  HomelessShelter: service_place_HomelessShelter,
  /**
   * A facility or location, owned and operated by the Indian Health Service, which provides diagnostic, therapeutic (surgical and nonsurgical), and rehabilitation services to American Indians and Alaska Natives who do not require hospitalization.
   */
  IndianHealthServiceFreeStandingFacility: service_place_IndianHealthServiceFreeStandingFacility,
  /**
   * A facility or location, owned and operated by the Indian Health Service, which provides diagnostic, therapeutic (surgical and nonsurgical), and rehabilitation services rendered by, or under the supervision of, physicians to American Indians and Alaska Natives admitted as inpatients or outpatients.
   */
  IndianHealthServiceProviderBasedFacility: service_place_IndianHealthServiceProviderBasedFacility,
  /**
   * A facility or location owned and operated by a federally recognized American Indian or Alaska Native tribe or tribal organization under a 638 agreement, which provides diagnostic, therapeutic (surgical and nonsurgical), and rehabilitation services to tribal members who do not require hospitalization.
   */
  Tribal638FreeStandingFacility: service_place_Tribal638FreeStandingFacility,
  /**
   * A facility or location owned and operated by a federally recognized American Indian or Alaska Native tribe or tribal organization under a 638 agreement, which provides diagnostic, therapeutic (surgical and nonsurgical), and rehabilitation services to tribal members admitted as inpatients or outpatients.
   */
  Tribal638ProviderBasedFacility: service_place_Tribal638ProviderBasedFacility,
  /**
   * A prison, jail, reformatory, work farm, detention center, or any other similar facility maintained by either Federal, State or local authorities for the purpose of confinement or rehabilitation of adult or juvenile criminal offenders.
   */
  PrisonCorrectionalFacility: service_place_PrisonCorrectionalFacility,
  /**
   * Location, other than a hospital, skilled nursing facility (SNF), military treatment facility, community health center, State or local public health clinic, or intermediate care facility (ICF), where the health professional routinely provides health examinations, diagnosis, and treatment of illness or injury on an ambulatory basis.
   */
  Office: service_place_Office,
  /**
   * Location, other than a hospital or other facility, where the patient receives care in a private residence.
   */
  Home: service_place_Home,
  /**
   * Congregate residential facility with self-contained living units providing assessment of each resident's needs and on-site support 24 hours a day, 7 days a week, with the capacity to deliver or arrange for services including some health care and other services.
   */
  AssistedLivingFa: service_place_AssistedLivingFa,
  /**
   * A residence, with shared living areas, where clients receive supervision and other services such as social and/or behavioral services, custodial service, and minimal services (e.g., medication administration).
   */
  GroupHome: service_place_GroupHome,
  /**
   * A facility/unit that moves from place-to-place equipped to provide preventive, screening, diagnostic, and/or treatment services.
   */
  MobileUnit: service_place_MobileUnit,
  /**
   * portion of an off-campus hospital provider-based department which provides diagnostic, therapeutic (both surgical and nonsurgical), and rehabilitation services to sick or injured persons who do not require hospitalization or institutionalization.
   */
  OffCampusOutpatientHospital: service_place_OffCampusOutpatientHospital,
  /**
   * Location, distinct from a hospital emergency room, an office, or a clinic, whose purpose is to diagnose and treat illness or injury for unscheduled, ambulatory patients seeking immediate medical attention.
   */
  UrgentCareFacility: service_place_UrgentCareFacility,
  /**
   * A facility, other than psychiatric, which primarily provides diagnostic, therapeutic (both surgical and nonsurgical), and rehabilitation services by, or under, the supervision of physicians to patients admitted for a variety of medical conditions.
   */
  InpatientHospital: service_place_InpatientHospital,
  /**
   * A land vehicle specifically designed, equipped and staffed for lifesaving and transporting the sick or injured.
   */
  AmbulanceLand: service_place_AmbulanceLand,
};
const service_product_Exam: Coding = {
  code: 'exam',
  display: 'Exam',
  system: 'http://hl7.org/fhir/ex-serviceproduct',
};
const service_product_FluShot: Coding = {
  code: 'flushot',
  display: 'Flu shot',
  system: 'http://hl7.org/fhir/ex-serviceproduct',
};
/**
 * This value set includes a smattering of Service/Product codes.
 */
export const ServiceProduct = {
  /**
   * Exam
   */
  Exam: service_product_Exam,
  /**
   * Flu shot
   */
  FluShot: service_product_FluShot,
};
const service_provision_conditions_FeesApply: Coding = {
  code: 'cost',
  display: 'Fees apply',
  system: 'http://terminology.hl7.org/CodeSystem/service-provision-conditions',
};
const service_provision_conditions_DiscountsAvailable: Coding = {
  code: 'disc',
  display: 'Discounts Available',
  system: 'http://terminology.hl7.org/CodeSystem/service-provision-conditions',
};
const service_provision_conditions_Free: Coding = {
  code: 'free',
  display: 'Free',
  system: 'http://terminology.hl7.org/CodeSystem/service-provision-conditions',
};
/**
 * The code(s) that detail the conditions under which the healthcare service is available/offered.
 */
export const ServiceProvisionConditions = {
  /**
   * Fees apply for this service.
   */
  FeesApply: service_provision_conditions_FeesApply,
  /**
   * There are discounts available on this service for qualifying patients.
   */
  DiscountsAvailable: service_provision_conditions_DiscountsAvailable,
  /**
   * This service is available for no patient cost.
   */
  Free: service_provision_conditions_Free,
};
const service_referral_method_SecureMessaging: Coding = {
  code: 'elec',
  display: 'Secure Messaging',
  system: 'http://terminology.hl7.org/CodeSystem/service-referral-method',
};
const service_referral_method_Fax: Coding = {
  code: 'fax',
  display: 'Fax',
  system: 'http://terminology.hl7.org/CodeSystem/service-referral-method',
};
const service_referral_method_Mail: Coding = {
  code: 'mail',
  display: 'Mail',
  system: 'http://terminology.hl7.org/CodeSystem/service-referral-method',
};
const service_referral_method_Phone: Coding = {
  code: 'phone',
  display: 'Phone',
  system: 'http://terminology.hl7.org/CodeSystem/service-referral-method',
};
const service_referral_method_SecureEmail: Coding = {
  code: 'semail',
  display: 'Secure Email',
  system: 'http://terminology.hl7.org/CodeSystem/service-referral-method',
};
/**
 * The methods of referral can be used when referring to a specific HealthCareService resource.
 */
export const ServiceReferralMethod = {
  /**
   * Referrals may be accepted via a secure messaging system. To determine the types of secure messaging systems supported, refer to the identifiers collection. Callers will need to understand the specific identifier system used to know that they are able to transmit messages.
   */
  SecureMessaging: service_referral_method_SecureMessaging,
  /**
   * Referrals may be accepted by fax.
   */
  Fax: service_referral_method_Fax,
  /**
   * Referrals may be accepted via regular postage (or hand delivered).
   */
  Mail: service_referral_method_Mail,
  /**
   * Referrals may be accepted over the phone from a practitioner.
   */
  Phone: service_referral_method_Phone,
  /**
   * Referrals may be accepted via a secure email. To send please encrypt with the services public key.
   */
  SecureEmail: service_referral_method_SecureEmail,
};
const ServicerequestCategory_LaboratoryProcedure: Coding = {
  code: '108252007',
  display: 'Laboratory procedure',
  system: 'http://snomed.info/sct',
};
const ServicerequestCategory_Imaging: Coding = {
  code: '363679005',
  display: 'Imaging',
  system: 'http://snomed.info/sct',
};
/**
 * An example value set of SNOMED CT concepts that can classify a requested service
 */
export const ServicerequestCategory = {
  LaboratoryProcedure: ServicerequestCategory_LaboratoryProcedure,
  Imaging: ServicerequestCategory_Imaging,
  VAL387713003: ProcedureCategory_VAL387713003,
  VAL409063005: ProcedureCategory_VAL409063005,
  VAL409073007: ProcedureCategory_VAL409073007,
};
const ServicerequestOrderdetail_PatientTriggeredInspiratoryAssistanceProcedure: Coding = {
  code: '243144002',
  display: 'Patient triggered inspiratory assistance (procedure)',
  system: 'http://snomed.info/sct',
};
const ServicerequestOrderdetail_AssistedControlledMandatoryVentilationProcedure: Coding = {
  code: '243150007',
  display: 'Assisted controlled mandatory ventilation (procedure)',
  system: 'http://snomed.info/sct',
};
const ServicerequestOrderdetail_PressureControlledVentilationProcedure: Coding = {
  code: '286812008',
  display: 'Pressure controlled ventilation (procedure)',
  system: 'http://snomed.info/sct',
};
const ServicerequestOrderdetail_ContinuousPositiveAirwayPressureVentilationTreatmentRegimeTherapy: Coding = {
  code: '47545007',
  display: 'Continuous positive airway pressure ventilation treatment (regime/therapy)',
  system: 'http://snomed.info/sct',
};
const ServicerequestOrderdetail_SynchronizedIntermittentMandatoryVentilationProcedure: Coding = {
  code: '59427005',
  display: 'Synchronized intermittent mandatory ventilation (procedure)',
  system: 'http://snomed.info/sct',
};
/**
 * An example value set of Codified order entry details concepts.  These concepts only make sense in the context of what is being ordered.  This example is for a patient ventilation order
 */
export const ServicerequestOrderdetail = {
  PatientTriggeredInspiratoryAssistanceProcedure: ServicerequestOrderdetail_PatientTriggeredInspiratoryAssistanceProcedure,
  AssistedControlledMandatoryVentilationProcedure: ServicerequestOrderdetail_AssistedControlledMandatoryVentilationProcedure,
  PressureControlledVentilationProcedure: ServicerequestOrderdetail_PressureControlledVentilationProcedure,
  ContinuousPositiveAirwayPressureVentilationTreatmentRegimeTherapy: ServicerequestOrderdetail_ContinuousPositiveAirwayPressureVentilationTreatmentRegimeTherapy,
  SynchronizedIntermittentMandatoryVentilationProcedure: ServicerequestOrderdetail_SynchronizedIntermittentMandatoryVentilationProcedure,
};
const service_type_AdoptionPermanentCareInfoSupport: Coding = {
  code: '1',
  display: 'Adoption/Permanent Care Info/Support',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PersonalAlarmsAlerts: Coding = {
  code: '10',
  display: 'Personal Alarms/Alerts',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DisabilityInformationReferral: Coding = {
  code: '100',
  display: 'Disability Information/Referral',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DisabilitySupportPackages: Coding = {
  code: '101',
  display: 'Disability Support Packages',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DisabilitySupportedAccommodation: Coding = {
  code: '102',
  display: 'Disability Supported Accommodation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_EarlyChildhoodIntervention: Coding = {
  code: '103',
  display: 'Early Childhood Intervention',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HearingAidsAndEquipment: Coding = {
  code: '104',
  display: 'Hearing Aids & Equipment',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DrugAndOrAlcoholCounselling: Coding = {
  code: '105',
  display: 'Drug and/or Alcohol Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DrugAlcoholInformationReferral: Coding = {
  code: '106',
  display: 'Drug/Alcohol Information/Referral',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_NeedleAndSyringeExchange: Coding = {
  code: '107',
  display: 'Needle & Syringe Exchange',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_NonResidAlcoholDrugTreatment: Coding = {
  code: '108',
  display: 'Non-resid. Alcohol/Drug Treatment ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Pharmacotherapy: Coding = {
  code: '109',
  display: 'Pharmacotherapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PersonalCareForOlderPersons: Coding = {
  code: '11',
  display: 'Personal Care for Older Persons',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_QuitProgram: Coding = {
  code: '110',
  display: 'Quit Program',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ResidentialAlcoholDrugTreatment: Coding = {
  code: '111',
  display: 'Residential Alcohol/Drug Treatment ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AdultCommunityEducation: Coding = {
  code: '112',
  display: 'Adult/Community Education',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HigherEducation: Coding = {
  code: '113',
  display: 'Higher Education',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PrimaryEducation: Coding = {
  code: '114',
  display: 'Primary Education',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SecondaryEducation: Coding = {
  code: '115',
  display: 'Secondary Education',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_TrainingAndVocationalEducation: Coding = {
  code: '116',
  display: 'Training & Vocational Education',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_EmergencyMedical: Coding = {
  code: '117',
  display: 'Emergency Medical',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_EmploymentPlacementAndOrSupport: Coding = {
  code: '118',
  display: 'Employment Placement and/or Support',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_VocationalRehabilitation: Coding = {
  code: '119',
  display: 'Vocational Rehabilitation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PlannedActivityGroups: Coding = {
  code: '12',
  display: 'Planned Activity Groups',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_WorkSafetyAccidentPrevention: Coding = {
  code: '120',
  display: 'Work Safety/Accident Prevention',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FinancialAssistance: Coding = {
  code: '121',
  display: 'Financial Assistance',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FinancialInformationAdvice: Coding = {
  code: '122',
  display: 'Financial Information/Advice',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MaterialAid: Coding = {
  code: '123',
  display: 'Material Aid',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_GeneralPractice: Coding = {
  code: '124',
  display: 'General Practice',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AccommodationPlacementSupport: Coding = {
  code: '125',
  display: 'Accommodation Placement/Support',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CrisisEmergencyAccommodation: Coding = {
  code: '126',
  display: 'Crisis/Emergency Accommodation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HomelessnessSupport: Coding = {
  code: '127',
  display: 'Homelessness Support',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HousingInformationReferral: Coding = {
  code: '128',
  display: 'Housing Information/Referral',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PublicRentalHousing: Coding = {
  code: '129',
  display: 'Public Rental Housing',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Acupuncture: Coding = {
  code: '13',
  display: 'Acupuncture',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_InterpretingMultilingualService: Coding = {
  code: '130',
  display: 'Interpreting/Multilingual Service',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_JuvenileJustice: Coding = {
  code: '131',
  display: 'Juvenile Justice',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_LegalAdvocacy: Coding = {
  code: '132',
  display: 'Legal Advocacy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_LegalInformationAdviceReferral: Coding = {
  code: '133',
  display: 'Legal Information/Advice/Referral',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MentalHealthAdvocacy: Coding = {
  code: '134',
  display: 'Mental Health Advocacy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MentalHealthAssessTriageCrisisResponse: Coding = {
  code: '135',
  display: 'Mental Health Assess/Triage/Crisis Response',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MentalHealthCaseManagement: Coding = {
  code: '136',
  display: 'Mental Health Case Management',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MentalHealthInformationReferral: Coding = {
  code: '137',
  display: 'Mental Health Information/Referral',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MentalHealthInpatientServices: Coding = {
  code: '138',
  display: 'Mental Health Inpatient Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MentalHealthNonResidentialRehab: Coding = {
  code: '139',
  display: 'Mental Health Non-residential Rehab',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AlexanderTechniqueTherapy: Coding = {
  code: '14',
  display: 'Alexander Technique Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MentalHealthResidentialRehabCCU: Coding = {
  code: '140',
  display: 'Mental Health Residential Rehab/CCU',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PsychiatryRequiresReferral: Coding = {
  code: '141',
  display: 'Psychiatry (Requires Referral)',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Psychology: Coding = {
  code: '142',
  display: 'Psychology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MartialArts: Coding = {
  code: '143',
  display: 'Martial Arts',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PersonalFitnessTraining: Coding = {
  code: '144',
  display: 'Personal Fitness Training',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PhysicalActivityGroup: Coding = {
  code: '145',
  display: 'Physical Activity Group',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PhysicalActivityPrograms: Coding = {
  code: '146',
  display: 'Physical Activity Programs',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PhysicalFitnessTesting: Coding = {
  code: '147',
  display: 'Physical Fitness Testing',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Pilates: Coding = {
  code: '148',
  display: 'Pilates',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SelfDefence: Coding = {
  code: '149',
  display: 'Self-Defence',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Aromatherapy: Coding = {
  code: '15',
  display: 'Aromatherapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SportingClub: Coding = {
  code: '150',
  display: 'Sporting Club',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Yoga: Coding = {
  code: '151',
  display: 'Yoga',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FoodSafety: Coding = {
  code: '152',
  display: 'Food Safety',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HealthRegulatoryInspectionCert: Coding = {
  code: '153',
  display: 'Health Regulatory /Inspection /Cert.',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_WorkHealthSafetyInspectionCert: Coding = {
  code: '154',
  display: 'Work Health/Safety Inspection/Cert.',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CarerSupport: Coding = {
  code: '155',
  display: 'Carer Support',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_RespiteCare: Coding = {
  code: '156',
  display: 'Respite Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AnatomicalPathology: Coding = {
  code: '157',
  display: 'Anatomical Pathology ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PathologyClinicalChemistry: Coding = {
  code: '158',
  display: 'Pathology - Clinical Chemistry',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PathologyGeneral: Coding = {
  code: '159',
  display: 'Pathology - General',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BiorhythmServices: Coding = {
  code: '16',
  display: 'Biorhythm Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PathologyGenetics: Coding = {
  code: '160',
  display: 'Pathology - Genetics',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PathologyHaematology: Coding = {
  code: '161',
  display: 'Pathology - Haematology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PathologyImmunology: Coding = {
  code: '162',
  display: 'Pathology - Immunology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PathologyMicrobiology: Coding = {
  code: '163',
  display: 'Pathology - Microbiology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AnaesthesiologyPainMedicine: Coding = {
  code: '164',
  display: 'Anaesthesiology - Pain Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Cardiology: Coding = {
  code: '165',
  display: 'Cardiology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ClinicalGenetics: Coding = {
  code: '166',
  display: 'Clinical Genetics',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ClinicalPharmacology: Coding = {
  code: '167',
  display: 'Clinical Pharmacology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Dermatology: Coding = {
  code: '168',
  display: 'Dermatology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Endocrinology: Coding = {
  code: '169',
  display: 'Endocrinology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BowenTherapy: Coding = {
  code: '17',
  display: 'Bowen Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_GastroenterologyAndHepatology: Coding = {
  code: '170',
  display: 'Gastroenterology & Hepatology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_GeriatricMedicine: Coding = {
  code: '171',
  display: 'Geriatric Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ImmunologyAndAllergy: Coding = {
  code: '172',
  display: 'Immunology & Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_InfectiousDiseases: Coding = {
  code: '173',
  display: 'Infectious Diseases',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_IntensiveCareMedicine: Coding = {
  code: '174',
  display: 'Intensive Care Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MedicalOncology: Coding = {
  code: '175',
  display: 'Medical Oncology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Nephrology: Coding = {
  code: '176',
  display: 'Nephrology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Neurology: Coding = {
  code: '177',
  display: 'Neurology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_OccupationalMedicine: Coding = {
  code: '178',
  display: 'Occupational Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PalliativeMedicine: Coding = {
  code: '179',
  display: 'Palliative Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ChineseHerbalMedicine: Coding = {
  code: '18',
  display: 'Chinese Herbal Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PublicHealthMedicine: Coding = {
  code: '180',
  display: 'Public Health Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_RehabilitationMedicine: Coding = {
  code: '181',
  display: 'Rehabilitation Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Rheumatology: Coding = {
  code: '182',
  display: 'Rheumatology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SleepMedicine: Coding = {
  code: '183',
  display: 'Sleep Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ThoracicMedicine: Coding = {
  code: '184',
  display: 'Thoracic Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_GynaecologicalOncology: Coding = {
  code: '185',
  display: 'Gynaecological Oncology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ObstetricsAndGynaecology: Coding = {
  code: '186',
  display: 'Obstetrics & Gynaecology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ReproductiveEndocrinologyInfertility: Coding = {
  code: '187',
  display: 'Reproductive Endocrinology/Infertility',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Urogynaecology: Coding = {
  code: '188',
  display: 'Urogynaecology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_NeonatologyAndPerinatology: Coding = {
  code: '189',
  display: 'Neonatology & Perinatology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Feldenkrais: Coding = {
  code: '19',
  display: 'Feldenkrais',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricCardiology: Coding = {
  code: '190',
  display: 'Paediatric Cardiology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricClinicalGenetics: Coding = {
  code: '191',
  display: 'Paediatric Clinical Genetics',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricClinicalPharmacology: Coding = {
  code: '192',
  display: 'Paediatric Clinical Pharmacology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricEndocrinology: Coding = {
  code: '193',
  display: 'Paediatric Endocrinology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaedGastroenterologyHepatology: Coding = {
  code: '194',
  display: 'Paed. Gastroenterology/Hepatology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricHaematology: Coding = {
  code: '195',
  display: 'Paediatric Haematology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricImmunologyAndAllergy: Coding = {
  code: '196',
  display: 'Paediatric Immunology & Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricInfectiousDiseases: Coding = {
  code: '197',
  display: 'Paediatric Infectious Diseases',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricIntensiveCareMedicine: Coding = {
  code: '198',
  display: 'Paediatric Intensive Care Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricMedicalOncology: Coding = {
  code: '199',
  display: 'Paediatric Medical Oncology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AgedCareAssessment: Coding = {
  code: '2',
  display: 'Aged Care Assessment',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Homoeopathy: Coding = {
  code: '20',
  display: 'Homoeopathy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricMedicine: Coding = {
  code: '200',
  display: 'Paediatric Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricNephrology: Coding = {
  code: '201',
  display: 'Paediatric Nephrology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricNeurology: Coding = {
  code: '202',
  display: 'Paediatric Neurology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricNuclearMedicine: Coding = {
  code: '203',
  display: 'Paediatric Nuclear Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricRehabilitationMedicine: Coding = {
  code: '204',
  display: 'Paediatric Rehabilitation Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricRheumatology: Coding = {
  code: '205',
  display: 'Paediatric Rheumatology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricSleepMedicine: Coding = {
  code: '206',
  display: 'Paediatric Sleep Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricSurgery: Coding = {
  code: '207',
  display: 'Paediatric Surgery',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricThoracicMedicine: Coding = {
  code: '208',
  display: 'Paediatric Thoracic Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DiagRadiologyXrayCTFluoroscopy: Coding = {
  code: '209',
  display: 'Diag. Radiology /Xray /CT /Fluoroscopy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Hydrotherapy: Coding = {
  code: '21',
  display: 'Hydrotherapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DiagnosticUltrasound: Coding = {
  code: '210',
  display: 'Diagnostic Ultrasound',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MagneticResonanceImagingMRI: Coding = {
  code: '211',
  display: 'Magnetic Resonance Imaging (MRI)',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_NuclearMedicine: Coding = {
  code: '212',
  display: 'Nuclear Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ObstetricGynaecologicalUltrasound: Coding = {
  code: '213',
  display: 'Obstetric/Gynaecological Ultrasound',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_RadiationOncology: Coding = {
  code: '214',
  display: 'Radiation Oncology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CardiothoracicSurgery: Coding = {
  code: '215',
  display: 'Cardiothoracic Surgery',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Neurosurgery: Coding = {
  code: '216',
  display: 'Neurosurgery',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Ophthalmology: Coding = {
  code: '217',
  display: 'Ophthalmology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_OrthopaedicSurgery: Coding = {
  code: '218',
  display: 'Orthopaedic Surgery',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_OtolaryngologyHeadAndNeckSurgery: Coding = {
  code: '219',
  display: 'Otolaryngology/Head & Neck Surgery',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Hypnotherapy: Coding = {
  code: '22',
  display: 'Hypnotherapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PlasticAndReconstructiveSurgery: Coding = {
  code: '220',
  display: 'Plastic & Reconstructive Surgery',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SurgeryGeneral: Coding = {
  code: '221',
  display: 'Surgery - General',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Urology: Coding = {
  code: '222',
  display: 'Urology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_VascularSurgery: Coding = {
  code: '223',
  display: 'Vascular Surgery',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SupportGroups: Coding = {
  code: '224',
  display: 'Support Groups',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AirAmbulance: Coding = {
  code: '225',
  display: 'Air ambulance',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Ambulance: Coding = {
  code: '226',
  display: 'Ambulance',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BloodTransport: Coding = {
  code: '227',
  display: 'Blood Transport',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CommunityBus: Coding = {
  code: '228',
  display: 'Community Bus',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FlyingDoctorService: Coding = {
  code: '229',
  display: 'Flying Doctor Service',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Kinesiology: Coding = {
  code: '23',
  display: 'Kinesiology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PatientTransport: Coding = {
  code: '230',
  display: 'Patient Transport',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AAndE: Coding = {
  code: '231',
  display: 'A&E',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AAndEP: Coding = {
  code: '232',
  display: 'A&EP',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Abuse: Coding = {
  code: '233',
  display: 'Abuse',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ACAS: Coding = {
  code: '234',
  display: 'ACAS',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Access: Coding = {
  code: '235',
  display: 'Access',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Accident: Coding = {
  code: '236',
  display: 'Accident',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AcuteInpatientServ: Coding = {
  code: '237',
  display: 'Acute Inpatient Serv',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AdultDayPrograms: Coding = {
  code: '238',
  display: 'Adult Day Programs',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AdultMentalHealthServices: Coding = {
  code: '239',
  display: 'Adult Mental Health Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MagneticTherapy: Coding = {
  code: '24',
  display: 'Magnetic Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Advice: Coding = {
  code: '240',
  display: 'Advice',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Advocacy: Coding = {
  code: '241',
  display: 'Advocacy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AgedPersonsMental: Coding = {
  code: '242',
  display: 'Aged Persons Mental ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AgedPersonsMental_2: Coding = {
  code: '243',
  display: 'Aged Persons Mental ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AgedPersonsMental_3: Coding = {
  code: '244',
  display: 'Aged Persons Mental ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Aids: Coding = {
  code: '245',
  display: 'Aids',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AlAnon: Coding = {
  code: '246',
  display: 'Al-Anon',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Alcohol: Coding = {
  code: '247',
  display: 'Alcohol',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AlTeen: Coding = {
  code: '248',
  display: 'Al-Teen',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Antenatal: Coding = {
  code: '249',
  display: 'Antenatal',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MassageTherapy: Coding = {
  code: '25',
  display: 'Massage Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Anxiety: Coding = {
  code: '250',
  display: 'Anxiety',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Arthritis: Coding = {
  code: '251',
  display: 'Arthritis',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Assessment: Coding = {
  code: '252',
  display: 'Assessment',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Assistance: Coding = {
  code: '253',
  display: 'Assistance',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Asthma: Coding = {
  code: '254',
  display: 'Asthma',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ATSS: Coding = {
  code: '255',
  display: 'ATSS',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AttendantCare: Coding = {
  code: '256',
  display: 'Attendant Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Babies: Coding = {
  code: '257',
  display: 'Babies',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BathroomModificatio: Coding = {
  code: '258',
  display: 'Bathroom Modificatio',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Behavior: Coding = {
  code: '259',
  display: 'Behavior',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Meditation: Coding = {
  code: '26',
  display: 'Meditation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BehaviorInterventi: Coding = {
  code: '260',
  display: 'Behavior Interventi',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Bereavement: Coding = {
  code: '261',
  display: 'Bereavement',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Bipolar: Coding = {
  code: '262',
  display: 'Bipolar',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Birth: Coding = {
  code: '263',
  display: 'Birth',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BirthControl: Coding = {
  code: '264',
  display: 'Birth Control',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BirthingOptions: Coding = {
  code: '265',
  display: 'Birthing Options',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BIST: Coding = {
  code: '266',
  display: 'BIST',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Blood: Coding = {
  code: '267',
  display: 'Blood',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Bone: Coding = {
  code: '268',
  display: 'Bone',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Bowel: Coding = {
  code: '269',
  display: 'Bowel',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Myotherapy: Coding = {
  code: '27',
  display: 'Myotherapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Brain: Coding = {
  code: '270',
  display: 'Brain',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BreastFeeding: Coding = {
  code: '271',
  display: 'Breast Feeding',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BreastScreen: Coding = {
  code: '272',
  display: 'Breast Screen',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Brokerage: Coding = {
  code: '273',
  display: 'Brokerage',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Cancer: Coding = {
  code: '274',
  display: 'Cancer',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CancerSupport: Coding = {
  code: '275',
  display: 'Cancer Support',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CardiovascularDisea: Coding = {
  code: '276',
  display: 'Cardiovascular Disea',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CarePackages: Coding = {
  code: '277',
  display: 'Care Packages',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Carer: Coding = {
  code: '278',
  display: 'Carer',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CaseManagement: Coding = {
  code: '279',
  display: 'Case Management',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Naturopathy: Coding = {
  code: '28',
  display: 'Naturopathy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Casualty: Coding = {
  code: '280',
  display: 'Casualty',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Centrelink: Coding = {
  code: '281',
  display: 'Centrelink',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Chemists: Coding = {
  code: '282',
  display: 'Chemists',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ChildAndAdolescent: Coding = {
  code: '283',
  display: 'Child And Adolescent',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ChildCare: Coding = {
  code: '284',
  display: 'Child Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ChildServices: Coding = {
  code: '285',
  display: 'Child Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Children: Coding = {
  code: '286',
  display: 'Children',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ChildrenQuoteSServices: Coding = {
  code: '287',
  display: "Children's Services",
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Cholesterol: Coding = {
  code: '288',
  display: 'Cholesterol',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Clothing: Coding = {
  code: '289',
  display: 'Clothing',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Reflexology: Coding = {
  code: '29',
  display: 'Reflexology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CommunityBasedAcco: Coding = {
  code: '290',
  display: 'Community Based Acco',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CommunityCareUnit: Coding = {
  code: '291',
  display: 'Community Care Unit',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CommunityChildAnd: Coding = {
  code: '292',
  display: 'Community Child And ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CommunityHealth: Coding = {
  code: '293',
  display: 'Community Health',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CommunityResidentia: Coding = {
  code: '294',
  display: 'Community Residentia',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CommunityTransport: Coding = {
  code: '295',
  display: 'Community Transport',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CompanionVisiting: Coding = {
  code: '296',
  display: 'Companion Visiting',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Companionship: Coding = {
  code: '297',
  display: 'Companionship',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ConsumerAdvice: Coding = {
  code: '298',
  display: 'Consumer Advice',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ConsumerIssues: Coding = {
  code: '299',
  display: 'Consumer Issues',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AgedCareInformationReferral: Coding = {
  code: '3',
  display: 'Aged Care Information/Referral',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Reiki: Coding = {
  code: '30',
  display: 'Reiki',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ContinuingCareServ: Coding = {
  code: '300',
  display: 'Continuing Care Serv',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ContraceptionInform: Coding = {
  code: '301',
  display: 'Contraception Inform',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CoordinatingBodies: Coding = {
  code: '302',
  display: 'Coordinating Bodies',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CorrectionalService: Coding = {
  code: '303',
  display: 'Correctional Service',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CouncilEnvironmenta: Coding = {
  code: '304',
  display: 'Council Environmenta',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Counselling: Coding = {
  code: '305',
  display: 'Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Criminal: Coding = {
  code: '306',
  display: 'Criminal',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Crises: Coding = {
  code: '307',
  display: 'Crises',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CrisisAssessmentAn: Coding = {
  code: '308',
  display: 'Crisis Assessment An',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CrisisAssistance: Coding = {
  code: '309',
  display: 'Crisis Assistance',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_RelaxationTherapy: Coding = {
  code: '31',
  display: 'Relaxation Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CrisisRefuge: Coding = {
  code: '310',
  display: 'Crisis Refuge',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DayProgram: Coding = {
  code: '311',
  display: 'Day Program',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Deaf: Coding = {
  code: '312',
  display: 'Deaf',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DentalHygiene: Coding = {
  code: '313',
  display: 'Dental Hygiene',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Dentistry: Coding = {
  code: '314',
  display: 'Dentistry',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Dentures: Coding = {
  code: '315',
  display: 'Dentures',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Depression: Coding = {
  code: '316',
  display: 'Depression',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Detoxification: Coding = {
  code: '317',
  display: 'Detoxification',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Diabetes: Coding = {
  code: '318',
  display: 'Diabetes',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DiaphragmFitting: Coding = {
  code: '319',
  display: 'Diaphragm Fitting',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Shiatsu: Coding = {
  code: '32',
  display: 'Shiatsu',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Dieticians: Coding = {
  code: '320',
  display: 'Dieticians',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DisabledParking: Coding = {
  code: '321',
  display: 'Disabled Parking',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DistrictNursing: Coding = {
  code: '322',
  display: 'District Nursing',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Divorce: Coding = {
  code: '323',
  display: 'Divorce',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Doctors: Coding = {
  code: '324',
  display: 'Doctors',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DrinkDrive: Coding = {
  code: '325',
  display: 'Drink-Drive',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DualDiagnosisServi: Coding = {
  code: '326',
  display: 'Dual Diagnosis Servi',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_EarlyChoice: Coding = {
  code: '327',
  display: 'Early Choice',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_EatingDisorder: Coding = {
  code: '328',
  display: 'Eating Disorder',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_WesternHerbalMedicine: Coding = {
  code: '33',
  display: 'Western Herbal Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_EmergencyRelief: Coding = {
  code: '330',
  display: 'Emergency Relief',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_EmploymentAndTrain: Coding = {
  code: '331',
  display: 'Employment And Train',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Environment: Coding = {
  code: '332',
  display: 'Environment',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Equipment: Coding = {
  code: '333',
  display: 'Equipment',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Exercise: Coding = {
  code: '334',
  display: 'Exercise',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Facility: Coding = {
  code: '335',
  display: 'Facility',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FamilyChoice: Coding = {
  code: '336',
  display: 'Family Choice',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FamilyLaw: Coding = {
  code: '337',
  display: 'Family Law',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FamilyOptions: Coding = {
  code: '338',
  display: 'Family Options',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FamilyServices: Coding = {
  code: '339',
  display: 'Family Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FamilyDayCare: Coding = {
  code: '34',
  display: 'Family Day care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FFYA: Coding = {
  code: '340',
  display: 'FFYA',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FinancialAid: Coding = {
  code: '341',
  display: 'Financial Aid',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Fitness: Coding = {
  code: '342',
  display: 'Fitness',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FlexibleCarePackag: Coding = {
  code: '343',
  display: 'Flexible Care Packag',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Food: Coding = {
  code: '344',
  display: 'Food',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FoodVouchers: Coding = {
  code: '345',
  display: 'Food Vouchers',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ForensicMentalHeal: Coding = {
  code: '346',
  display: 'Forensic Mental Heal',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Futures: Coding = {
  code: '347',
  display: 'Futures',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FuturesForYoungAd: Coding = {
  code: '348',
  display: 'Futures For Young Ad',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_GeneralPractitioner: Coding = {
  code: '349',
  display: 'General Practitioner',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HolidayPrograms: Coding = {
  code: '35',
  display: 'Holiday Programs',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Grants: Coding = {
  code: '350',
  display: 'Grants',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Grief: Coding = {
  code: '351',
  display: 'Grief',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_GriefCounselling: Coding = {
  code: '352',
  display: 'Grief Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HACC: Coding = {
  code: '353',
  display: 'HACC',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HeartDisease: Coding = {
  code: '354',
  display: 'Heart Disease',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Help: Coding = {
  code: '355',
  display: 'Help',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HighBloodPressure: Coding = {
  code: '356',
  display: 'High Blood Pressure',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HomeHelp: Coding = {
  code: '357',
  display: 'Home Help',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HomeNursing: Coding = {
  code: '358',
  display: 'Home Nursing',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Homefirst: Coding = {
  code: '359',
  display: 'Homefirst',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_KindergartenInclusionSupport: Coding = {
  code: '36',
  display: 'Kindergarten Inclusion Support ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HospiceCare: Coding = {
  code: '360',
  display: 'Hospice Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HospitalServices: Coding = {
  code: '361',
  display: 'Hospital Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HospitalToHome: Coding = {
  code: '362',
  display: 'Hospital To Home',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Hostel: Coding = {
  code: '364',
  display: 'Hostel',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HostelAccommodation: Coding = {
  code: '365',
  display: 'Hostel Accommodation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HouseholdItems: Coding = {
  code: '366',
  display: 'Household Items',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Hypertension: Coding = {
  code: '367',
  display: 'Hypertension',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Illness: Coding = {
  code: '368',
  display: 'Illness',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_IndependentLiving: Coding = {
  code: '369',
  display: 'Independent Living',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_KindergartenPreschool: Coding = {
  code: '37',
  display: 'Kindergarten/Preschool',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Information: Coding = {
  code: '370',
  display: 'Information',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Injury: Coding = {
  code: '371',
  display: 'Injury',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Intake: Coding = {
  code: '372',
  display: 'Intake',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_IntensiveMobileYou: Coding = {
  code: '373',
  display: 'Intensive Mobile You',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Intervention: Coding = {
  code: '374',
  display: 'Intervention',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_JobSearching: Coding = {
  code: '375',
  display: 'Job Searching',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Justice: Coding = {
  code: '376',
  display: 'Justice',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Leisure: Coding = {
  code: '377',
  display: 'Leisure',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Loans: Coding = {
  code: '378',
  display: 'Loans',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_LowIncomeEarners: Coding = {
  code: '379',
  display: 'Low Income Earners',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_LongDayChildCare: Coding = {
  code: '38',
  display: 'Long Day Child Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Lung: Coding = {
  code: '380',
  display: 'Lung',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MakingADifference: Coding = {
  code: '381',
  display: 'Making A Difference',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MedicalServices: Coding = {
  code: '382',
  display: 'Medical Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MedicalSpecialists: Coding = {
  code: '383',
  display: 'Medical Specialists',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MedicationAdministr: Coding = {
  code: '384',
  display: 'Medication Administr',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MenstrualInformatio: Coding = {
  code: '385',
  display: 'Menstrual Informatio',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Methadone: Coding = {
  code: '386',
  display: 'Methadone',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MobileSupportAndT: Coding = {
  code: '387',
  display: 'Mobile Support And T',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MotorNeurone: Coding = {
  code: '388',
  display: 'Motor Neurone',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MultipleSclerosis: Coding = {
  code: '389',
  display: 'Multiple Sclerosis',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_OccasionalChildCare: Coding = {
  code: '39',
  display: 'Occasional Child Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_NeighbourhoodHouse: Coding = {
  code: '390',
  display: 'Neighbourhood House',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_NursingHome: Coding = {
  code: '391',
  display: 'Nursing Home',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_NursingMothers: Coding = {
  code: '392',
  display: 'Nursing Mothers',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Obesity: Coding = {
  code: '393',
  display: 'Obesity',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_OccupationalHealth: Coding = {
  code: '394',
  display: 'Occupational Health ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Optometrist: Coding = {
  code: '395',
  display: 'Optometrist',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_OralHygiene: Coding = {
  code: '396',
  display: 'Oral Hygiene',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Outpatients: Coding = {
  code: '397',
  display: 'Outpatients',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_OutreachService: Coding = {
  code: '398',
  display: 'Outreach Service',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PADP: Coding = {
  code: '399',
  display: 'PADP',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AgedResidentialCare: Coding = {
  code: '4',
  display: 'Aged Residential Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_OutsideSchoolHoursCare: Coding = {
  code: '40',
  display: 'Outside School Hours Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Pain: Coding = {
  code: '400',
  display: 'Pain',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PapSmear: Coding = {
  code: '401',
  display: 'Pap Smear',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Parenting: Coding = {
  code: '402',
  display: 'Parenting',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PeakOrganizations: Coding = {
  code: '403',
  display: 'Peak Organizations',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PersonalCare: Coding = {
  code: '404',
  display: 'Personal Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Pharmacies: Coding = {
  code: '405',
  display: 'Pharmacies',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Phobias: Coding = {
  code: '406',
  display: 'Phobias',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Physical: Coding = {
  code: '407',
  display: 'Physical',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PhysicalActivity: Coding = {
  code: '408',
  display: 'Physical Activity',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Postnatal: Coding = {
  code: '409',
  display: 'Postnatal',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ChildrenQuoteSPlayPrograms: Coding = {
  code: '41',
  display: "Children's Play Programs",
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Pregnancy: Coding = {
  code: '410',
  display: 'Pregnancy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PregnancyTests: Coding = {
  code: '411',
  display: 'Pregnancy Tests',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Preschool: Coding = {
  code: '412',
  display: 'Preschool',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Prescriptions: Coding = {
  code: '413',
  display: 'Prescriptions',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PrimaryMentalHealt: Coding = {
  code: '414',
  display: 'Primary Mental Healt',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PropertyMaintenance: Coding = {
  code: '415',
  display: 'Property Maintenance',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Prostate: Coding = {
  code: '416',
  display: 'Prostate',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Psychiatric: Coding = {
  code: '417',
  display: 'Psychiatric',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PsychiatricDisabili: Coding = {
  code: '418',
  display: 'Psychiatric Disabili',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PsychiatricDisabili_2: Coding = {
  code: '419',
  display: 'Psychiatric Disabili',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ParentingFamilySupportEducation: Coding = {
  code: '42',
  display: 'Parenting/Family Support/Education',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PsychiatricDisabili_3: Coding = {
  code: '420',
  display: 'Psychiatric Disabili',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PsychiatricDisabili_4: Coding = {
  code: '421',
  display: 'Psychiatric Disabili',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PsychiatricDisabili_5: Coding = {
  code: '422',
  display: 'Psychiatric Disabili',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PsychiatricSupport: Coding = {
  code: '423',
  display: 'Psychiatric Support',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Recreation: Coding = {
  code: '424',
  display: 'Recreation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Referral: Coding = {
  code: '425',
  display: 'Referral',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Refuge: Coding = {
  code: '426',
  display: 'Refuge',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_RentAssistance: Coding = {
  code: '427',
  display: 'Rent Assistance',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ResidentialFaciliti: Coding = {
  code: '428',
  display: 'Residential Faciliti',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ResidentialRespite: Coding = {
  code: '429',
  display: 'Residential Respite',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Playgroup: Coding = {
  code: '43',
  display: 'Playgroup',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Respiratory: Coding = {
  code: '430',
  display: 'Respiratory',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Response: Coding = {
  code: '431',
  display: 'Response',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_RoomingHouses: Coding = {
  code: '432',
  display: 'Rooming Houses',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SafeSex: Coding = {
  code: '433',
  display: 'Safe Sex',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SecureExtendedCare: Coding = {
  code: '434',
  display: 'Secure Extended Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SelfHelp: Coding = {
  code: '435',
  display: 'Self Help',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Separation: Coding = {
  code: '436',
  display: 'Separation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Services: Coding = {
  code: '437',
  display: 'Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SexEducation: Coding = {
  code: '438',
  display: 'Sex Education',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SexualAbuse: Coding = {
  code: '439',
  display: 'Sexual Abuse',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SchoolNursing: Coding = {
  code: '44',
  display: 'School Nursing',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SexualIssues: Coding = {
  code: '440',
  display: 'Sexual Issues',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SexuallyTransmitted: Coding = {
  code: '441',
  display: 'Sexually Transmitted',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SIDS: Coding = {
  code: '442',
  display: 'SIDS',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SocialSupport: Coding = {
  code: '443',
  display: 'Social Support',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Socialisation: Coding = {
  code: '444',
  display: 'Socialisation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SpecialNeeds: Coding = {
  code: '445',
  display: 'Special Needs',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SpeechTherapist: Coding = {
  code: '446',
  display: 'Speech Therapist',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Splinting: Coding = {
  code: '447',
  display: 'Splinting',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Sport: Coding = {
  code: '448',
  display: 'Sport',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_StatewideAndSpecia: Coding = {
  code: '449',
  display: 'Statewide And Specia',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ToyLibrary: Coding = {
  code: '45',
  display: 'Toy Library',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_STD: Coding = {
  code: '450',
  display: 'STD',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_STI: Coding = {
  code: '451',
  display: 'STI',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Stillbirth: Coding = {
  code: '452',
  display: 'Stillbirth',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_StomalCare: Coding = {
  code: '453',
  display: 'Stomal Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Stroke: Coding = {
  code: '454',
  display: 'Stroke',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SubstanceAbuse: Coding = {
  code: '455',
  display: 'Substance Abuse',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Support: Coding = {
  code: '456',
  display: 'Support',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Syringes: Coding = {
  code: '457',
  display: 'Syringes',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Teeth: Coding = {
  code: '458',
  display: 'Teeth',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_TenancyAdvice: Coding = {
  code: '459',
  display: 'Tenancy Advice',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ChildProtectionChildAbuseReport: Coding = {
  code: '46',
  display: 'Child Protection/Child Abuse Report',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_TerminalIllness: Coding = {
  code: '460',
  display: 'Terminal Illness',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Therapy: Coding = {
  code: '461',
  display: 'Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Transcription: Coding = {
  code: '462',
  display: 'Transcription',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_TranslatingServices: Coding = {
  code: '463',
  display: 'Translating Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Translator: Coding = {
  code: '464',
  display: 'Translator',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Transport: Coding = {
  code: '465',
  display: 'Transport',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Vertebrae: Coding = {
  code: '466',
  display: 'Vertebrae',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Violence: Coding = {
  code: '467',
  display: 'Violence',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_VocationalGuidance: Coding = {
  code: '468',
  display: 'Vocational Guidance',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Weight: Coding = {
  code: '469',
  display: 'Weight',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FosterCare: Coding = {
  code: '47',
  display: 'Foster Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_WelfareAssistance: Coding = {
  code: '470',
  display: 'Welfare Assistance',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_WelfareCounselling: Coding = {
  code: '471',
  display: 'Welfare Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Wheelchairs: Coding = {
  code: '472',
  display: 'Wheelchairs',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_WoundManagement: Coding = {
  code: '473',
  display: 'Wound Management',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_YoungPeopleAtRisk: Coding = {
  code: '474',
  display: 'Young People At Risk',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FurtherDescCommunityHealthCare: Coding = {
  code: '475',
  display: 'Further Desc. - Community Health Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Library: Coding = {
  code: '476',
  display: 'Library',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CommunityHours: Coding = {
  code: '477',
  display: 'Community Hours',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FurtherDescSpecialistMedical: Coding = {
  code: '478',
  display: 'Further Desc. - Specialist Medical',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Hepatology: Coding = {
  code: '479',
  display: 'Hepatology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ResidentialOutOfHomeCare: Coding = {
  code: '48',
  display: 'Residential/Out-of-Home Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Gastroenterology: Coding = {
  code: '480',
  display: 'Gastroenterology ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Gynaecology: Coding = {
  code: '481',
  display: 'Gynaecology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Obstetrics: Coding = {
  code: '482',
  display: 'Obstetrics',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FurtherDescSpecialistSurgical: Coding = {
  code: '483',
  display: 'Further Desc. - Specialist Surgical',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PlacementProtection: Coding = {
  code: '484',
  display: 'Placement Protection',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FamilyViolence: Coding = {
  code: '485',
  display: 'Family Violence',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_IntegratedFamilyServices: Coding = {
  code: '486',
  display: 'Integrated Family Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DiabetesEducator: Coding = {
  code: '488',
  display: 'Diabetes Educator',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_KinshipCare: Coding = {
  code: '489',
  display: 'Kinship Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SupportYoungPeopleLeavingCare: Coding = {
  code: '49',
  display: 'Support - Young People Leaving Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_GeneralMentalHealthServices: Coding = {
  code: '490',
  display: 'General Mental Health Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ExercisePhysiology: Coding = {
  code: '491',
  display: 'Exercise Physiology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MedicalResearch: Coding = {
  code: '492',
  display: 'Medical Research',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Youth: Coding = {
  code: '493',
  display: 'Youth',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_YouthServices: Coding = {
  code: '494',
  display: 'Youth Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_YouthHealth: Coding = {
  code: '495',
  display: 'Youth Health',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ChildAndFamilySer: Coding = {
  code: '496',
  display: 'Child and Family Ser',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HomeVisits: Coding = {
  code: '497',
  display: 'Home Visits',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MobileServices: Coding = {
  code: '498',
  display: 'Mobile Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CaseManagementForOlderPersons: Coding = {
  code: '5',
  display: 'Case Management for Older Persons',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Audiology: Coding = {
  code: '50',
  display: 'Audiology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BeforeAndOrAfter: Coding = {
  code: '500',
  display: 'Before and/or After ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CancerServices: Coding = {
  code: '501',
  display: 'Cancer Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_IntegratedCancerSe: Coding = {
  code: '502',
  display: 'Integrated Cancer Se',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MultidisciplinarySe: Coding = {
  code: '503',
  display: 'Multidisciplinary Se',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MultidisciplinaryCa: Coding = {
  code: '504',
  display: 'Multidisciplinary Ca',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Meetings: Coding = {
  code: '505',
  display: 'Meetings',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BloodPressureMonit: Coding = {
  code: '506',
  display: 'Blood pressure monit',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DoseAdministration: Coding = {
  code: '507',
  display: 'Dose administration ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MedicalEquipmentHi: Coding = {
  code: '508',
  display: 'Medical Equipment Hi',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ParentingFamilySupportEducation_2: Coding = {
  code: '509',
  display: 'Parenting/Family Support/Education',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BloodDonation: Coding = {
  code: '51',
  display: 'Blood Donation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DeputisingService: Coding = {
  code: '510',
  display: 'Deputising Service',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CancerSupportGroups: Coding = {
  code: '513',
  display: 'Cancer Support Groups',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CommunityCancerServices: Coding = {
  code: '514',
  display: 'Community Cancer Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Chiropractic: Coding = {
  code: '52',
  display: 'Chiropractic',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Dietetics: Coding = {
  code: '53',
  display: 'Dietetics',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DisabilityCareTransport: Coding = {
  code: '530',
  display: 'Disability Care Transport',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AgedCareTransport: Coding = {
  code: '531',
  display: 'Aged Care Transport',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DiabetesEducationS: Coding = {
  code: '532',
  display: 'Diabetes Education s',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CardiacRehabilitati: Coding = {
  code: '533',
  display: 'Cardiac Rehabilitati',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_YoungAdultDiabetes: Coding = {
  code: '534',
  display: 'Young Adult Diabetes',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PulmonaryRehabilita: Coding = {
  code: '535',
  display: 'Pulmonary Rehabilita',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ArtTherapy: Coding = {
  code: '536',
  display: 'Art therapy ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MedicationReviews: Coding = {
  code: '537',
  display: 'Medication Reviews',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_TelephoneCounselling: Coding = {
  code: '538',
  display: 'Telephone Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_TelephoneHelpLine: Coding = {
  code: '539',
  display: 'Telephone Help Line',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FamilyPlanning: Coding = {
  code: '54',
  display: 'Family Planning',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_OnlineService: Coding = {
  code: '540',
  display: 'Online Service',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CrisisMentalHealth: Coding = {
  code: '541',
  display: 'Crisis - Mental Health',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_YouthCrisis: Coding = {
  code: '542',
  display: 'Youth Crisis',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SexualAssault: Coding = {
  code: '543',
  display: 'Sexual Assault',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_GPAHOther: Coding = {
  code: '544',
  display: 'GPAH Other',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricDermatology: Coding = {
  code: '545',
  display: 'Paediatric Dermatology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_VeteransServices: Coding = {
  code: '546',
  display: 'Veterans Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Veterans: Coding = {
  code: '547',
  display: 'Veterans',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FoodReliefFoodMeals: Coding = {
  code: '548',
  display: 'Food Relief/Food/Meals',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HealthAdvocacyLiaisonService: Coding = {
  code: '55',
  display: 'Health Advocacy/Liaison Service',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DementiaCare: Coding = {
  code: '550',
  display: 'Dementia Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Alzheimer: Coding = {
  code: '551',
  display: 'Alzheimer',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DrugAndOrAlcoholSupportGroups: Coding = {
  code: '552',
  display: 'Drug and/or Alcohol Support Groups',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_VAL1On1SupportMentoringCoaching: Coding = {
  code: '553',
  display: '1-on-1 Support /Mentoring /Coaching',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ChronicDiseaseManagement: Coding = {
  code: '554',
  display: 'Chronic Disease Management',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_LiaisonServices: Coding = {
  code: '555',
  display: 'Liaison Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_WalkInCentreNonEmergency: Coding = {
  code: '556',
  display: 'Walk-in Centre /Non-Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Inpatients: Coding = {
  code: '557',
  display: 'Inpatients',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SpiritualCounselling: Coding = {
  code: '558',
  display: 'Spiritual Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_WomenQuoteSHealth: Coding = {
  code: '559',
  display: "Women's Health",
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HealthInformationReferral: Coding = {
  code: '56',
  display: 'Health Information/Referral',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MenQuoteSHealth: Coding = {
  code: '560',
  display: "Men's Health",
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HealthEducationAwarenessProgram: Coding = {
  code: '561',
  display: 'Health Education/Awareness Program',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_TestMessage: Coding = {
  code: '562',
  display: 'Test Message',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_RemedialMassage: Coding = {
  code: '563',
  display: 'Remedial Massage',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AdolescentMentalHealthServices: Coding = {
  code: '564',
  display: 'Adolescent Mental Health Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_YouthDropInAssistanceSupport: Coding = {
  code: '565',
  display: 'Youth Drop In/Assistance/Support',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AboriginalHealthWorker: Coding = {
  code: '566',
  display: 'Aboriginal Health Worker',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_WomenQuoteSHealthClinic: Coding = {
  code: '567',
  display: "Women's Health Clinic",
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MenQuoteSHealthClinic: Coding = {
  code: '568',
  display: "Men's Health Clinic",
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MigrantHealthClinic: Coding = {
  code: '569',
  display: 'Migrant Health Clinic',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Immunization: Coding = {
  code: '57',
  display: 'Immunization',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_RefugeeHealthClinic: Coding = {
  code: '570',
  display: 'Refugee Health Clinic',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AboriginalHealthClinic: Coding = {
  code: '571',
  display: 'Aboriginal Health Clinic',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_NursePractitionerLeadClinicS: Coding = {
  code: '572',
  display: 'Nurse Practitioner Lead Clinic/s',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_NurseLeadClinicS: Coding = {
  code: '573',
  display: 'Nurse Lead Clinic/s',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CulturallyTailoredSupportGroups: Coding = {
  code: '574',
  display: 'Culturally Tailored Support Groups',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CulturallyTailoredHealthPromotion: Coding = {
  code: '575',
  display: 'Culturally Tailored Health Promotion',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Rehabilitation: Coding = {
  code: '576',
  display: 'Rehabilitation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_EducationInformationReferral: Coding = {
  code: '577',
  display: 'Education Information/Referral',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MaternalAndChildHealth: Coding = {
  code: '58',
  display: 'Maternal & Child Health',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SocialWork: Coding = {
  code: '580',
  display: 'Social Work',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Haematology: Coding = {
  code: '581',
  display: 'Haematology',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MaternitySharedCar: Coding = {
  code: '582',
  display: 'Maternity Shared Car',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_RehabilitationServi: Coding = {
  code: '583',
  display: 'Rehabilitation Servi',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CranioSacralTherapy: Coding = {
  code: '584',
  display: 'Cranio-sacral Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ProstheticsAndOrthotics: Coding = {
  code: '585',
  display: 'Prosthetics & Orthotics',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HomeMedicineReview: Coding = {
  code: '589',
  display: 'Home Medicine Review',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Nursing: Coding = {
  code: '59',
  display: 'Nursing',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_GPAHMedical: Coding = {
  code: '590',
  display: 'GPAH - Medical',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_MusicTherapy: Coding = {
  code: '591',
  display: 'Music Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FallsPrevention: Coding = {
  code: '593',
  display: 'Falls Prevention',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AccommodationTenancy: Coding = {
  code: '599',
  display: 'Accommodation/Tenancy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DeliveredMealsMealsOnWheels: Coding = {
  code: '6',
  display: 'Delivered Meals (Meals On Wheels)',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Nutrition: Coding = {
  code: '60',
  display: 'Nutrition',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AssessSkillAbilityNeeds: Coding = {
  code: '600',
  display: 'Assess-Skill, Ability, Needs',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AssistAccessMaintainEmploy: Coding = {
  code: '601',
  display: 'Assist Access/Maintain Employ',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AssistProdPersCareSafety: Coding = {
  code: '602',
  display: 'Assist Prod-Pers Care/Safety',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AssistIntegrateSchoolEd: Coding = {
  code: '603',
  display: 'Assist-Integrate School/Ed',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AssistLifeStageTransition: Coding = {
  code: '604',
  display: 'Assist-Life Stage, Transition',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AssistPersonalActivities: Coding = {
  code: '605',
  display: 'Assist-Personal Activities',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AssistTravelTransport: Coding = {
  code: '606',
  display: 'Assist-Travel/Transport',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AssistiveEquipGeneralTasks: Coding = {
  code: '607',
  display: 'Assistive Equip-General Tasks',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AssistiveEquipRecreation: Coding = {
  code: '608',
  display: 'Assistive Equip-Recreation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AssistiveProdHouseholdTask: Coding = {
  code: '609',
  display: 'Assistive Prod-Household Task',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_OccupationalTherapy: Coding = {
  code: '61',
  display: 'Occupational Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BehaviorSupport: Coding = {
  code: '610',
  display: 'Behavior Support',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CommsAndInfoEquipment: Coding = {
  code: '611',
  display: 'Comms & Info Equipment',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CommunityNursingCare: Coding = {
  code: '612',
  display: 'Community Nursing Care',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DailyTasksSharedLiving: Coding = {
  code: '613',
  display: 'Daily Tasks/Shared Living',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DevelopmentLifeSkills: Coding = {
  code: '614',
  display: 'Development-Life Skills',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_EarlyChildhoodSupports: Coding = {
  code: '615',
  display: 'Early Childhood Supports',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_EquipmentSpecialAssessSetup: Coding = {
  code: '616',
  display: 'Equipment Special Assess Setup',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HearingEquipment: Coding = {
  code: '617',
  display: 'Hearing Equipment',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HomeModification: Coding = {
  code: '618',
  display: 'Home Modification',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HouseholdTasks: Coding = {
  code: '619',
  display: 'Household Tasks',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Optometry: Coding = {
  code: '62',
  display: 'Optometry',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_InterpretTranslate: Coding = {
  code: '620',
  display: 'Interpret/Translate',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_OtherInnovativeSupports: Coding = {
  code: '621',
  display: 'Other Innovative Supports',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ParticipateCommunity: Coding = {
  code: '622',
  display: 'Participate Community',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PersonalMobilityEquipment: Coding = {
  code: '623',
  display: 'Personal Mobility Equipment',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PhysicalWellbeing: Coding = {
  code: '624',
  display: 'Physical Wellbeing',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PlanManagement: Coding = {
  code: '625',
  display: 'Plan Management',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_TherapeuticSupports: Coding = {
  code: '626',
  display: 'Therapeutic Supports',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_TrainingTravelIndependence: Coding = {
  code: '627',
  display: 'Training-Travel Independence',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_VehicleModifications: Coding = {
  code: '628',
  display: 'Vehicle modifications',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_VisionEquipment: Coding = {
  code: '629',
  display: 'Vision Equipment',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Osteopathy: Coding = {
  code: '63',
  display: 'Osteopathy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Pharmacy: Coding = {
  code: '64',
  display: 'Pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Physiotherapy: Coding = {
  code: '65',
  display: 'Physiotherapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Podiatry: Coding = {
  code: '66',
  display: 'Podiatry',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SexualHealth: Coding = {
  code: '67',
  display: 'Sexual Health',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SpeechPathologyTherapy: Coding = {
  code: '68',
  display: 'Speech Pathology/Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_BereavementCounselling: Coding = {
  code: '69',
  display: 'Bereavement Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FriendlyVisiting: Coding = {
  code: '7',
  display: 'Friendly Visiting',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CrisisCounselling: Coding = {
  code: '70',
  display: 'Crisis Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FamilyCounsellingTherapy: Coding = {
  code: '71',
  display: 'Family Counselling/Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FamilyViolenceCounselling: Coding = {
  code: '72',
  display: 'Family Violence Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FinancialCounselling: Coding = {
  code: '73',
  display: 'Financial Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_GeneralistCounselling: Coding = {
  code: '74',
  display: 'Generalist Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_GeneticCounselling: Coding = {
  code: '75',
  display: 'Genetic Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HealthCounselling: Coding = {
  code: '76',
  display: 'Health Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Mediation: Coding = {
  code: '77',
  display: 'Mediation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_ProblemGamblingCounselling: Coding = {
  code: '78',
  display: 'Problem Gambling Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_RelationshipCounselling: Coding = {
  code: '79',
  display: 'Relationship Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HomeCareHousekeepingAssistance: Coding = {
  code: '8',
  display: 'Home Care/Housekeeping Assistance',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_SexualAssaultCounselling: Coding = {
  code: '80',
  display: 'Sexual Assault Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_TraumaCounselling: Coding = {
  code: '81',
  display: 'Trauma Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_VictimsOfCrimeCounselling: Coding = {
  code: '82',
  display: 'Victims of Crime Counselling',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_CemeteryOperation: Coding = {
  code: '83',
  display: 'Cemetery Operation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Cremation: Coding = {
  code: '84',
  display: 'Cremation',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DeathServiceInformation: Coding = {
  code: '85',
  display: 'Death Service Information',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_FuneralServices: Coding = {
  code: '86',
  display: 'Funeral Services',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Endodontic: Coding = {
  code: '87',
  display: 'Endodontic',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_GeneralDental: Coding = {
  code: '88',
  display: 'General Dental',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_OralMedicine: Coding = {
  code: '89',
  display: 'Oral Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_HomeMaintenanceAndRepair: Coding = {
  code: '9',
  display: 'Home Maintenance and Repair',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_OralSurgery: Coding = {
  code: '90',
  display: 'Oral Surgery',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Orthodontic: Coding = {
  code: '91',
  display: 'Orthodontic',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_PaediatricDentistry: Coding = {
  code: '92',
  display: 'Paediatric Dentistry',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Periodontic: Coding = {
  code: '93',
  display: 'Periodontic',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_Prosthodontic: Coding = {
  code: '94',
  display: 'Prosthodontic',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_AcquiredBrainInjuryInfoReferral: Coding = {
  code: '95',
  display: 'Acquired Brain Injury Info/Referral',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DisabilityAdvocacy: Coding = {
  code: '96',
  display: 'Disability Advocacy',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DisabilityAidsAndEquipment: Coding = {
  code: '97',
  display: 'Disability Aids & Equipment',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DisabilityCaseManagement: Coding = {
  code: '98',
  display: 'Disability Case Management',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
const service_type_DisabilityDayProgramsActivities: Coding = {
  code: '99',
  display: 'Disability Day Programs/Activities',
  system: 'http://terminology.hl7.org/CodeSystem/service-type',
};
/**
 * This value set defines an example set of codes of service-types.
 */
export const ServiceType = {
  /**
   * Adoption & permanent care information/support
   */
  AdoptionPermanentCareInfoSupport: service_type_AdoptionPermanentCareInfoSupport,
  /**
   * Personal alarms/alerts
   */
  PersonalAlarmsAlerts: service_type_PersonalAlarmsAlerts,
  /**
   * Disability information/referral
   */
  DisabilityInformationReferral: service_type_DisabilityInformationReferral,
  /**
   * Disability support packages
   */
  DisabilitySupportPackages: service_type_DisabilitySupportPackages,
  /**
   * Disability supported accommodation
   */
  DisabilitySupportedAccommodation: service_type_DisabilitySupportedAccommodation,
  /**
   * Early childhood intervention
   */
  EarlyChildhoodIntervention: service_type_EarlyChildhoodIntervention,
  /**
   * Hearing aids & equipment
   */
  HearingAidsAndEquipment: service_type_HearingAidsAndEquipment,
  /**
   * Drug and/or alcohol counselling
   */
  DrugAndOrAlcoholCounselling: service_type_DrugAndOrAlcoholCounselling,
  /**
   * Drug and/or alcohol information/referral
   */
  DrugAlcoholInformationReferral: service_type_DrugAlcoholInformationReferral,
  /**
   * Needle & Syringe exchange
   */
  NeedleAndSyringeExchange: service_type_NeedleAndSyringeExchange,
  /**
   * Non-residential alcohol and/or drug dependence treatment
   */
  NonResidAlcoholDrugTreatment: service_type_NonResidAlcoholDrugTreatment,
  /**
   * Pharmacotherapy (eg. methadone) program
   */
  Pharmacotherapy: service_type_Pharmacotherapy,
  /**
   * Personal care for older persons
   */
  PersonalCareForOlderPersons: service_type_PersonalCareForOlderPersons,
  /**
   * Quit program
   */
  QuitProgram: service_type_QuitProgram,
  /**
   * Residential alcohol and/or drug dependence treatment
   */
  ResidentialAlcoholDrugTreatment: service_type_ResidentialAlcoholDrugTreatment,
  /**
   * Adult/community education
   */
  AdultCommunityEducation: service_type_AdultCommunityEducation,
  /**
   * Higher education
   */
  HigherEducation: service_type_HigherEducation,
  /**
   * Primary education
   */
  PrimaryEducation: service_type_PrimaryEducation,
  /**
   * Secondary education
   */
  SecondaryEducation: service_type_SecondaryEducation,
  /**
   * Training & vocational education
   */
  TrainingAndVocationalEducation: service_type_TrainingAndVocationalEducation,
  /**
   * Emergency medical
   */
  EmergencyMedical: service_type_EmergencyMedical,
  /**
   * Employment placement and/or support
   */
  EmploymentPlacementAndOrSupport: service_type_EmploymentPlacementAndOrSupport,
  /**
   * Vocational Rehabilitation
   */
  VocationalRehabilitation: service_type_VocationalRehabilitation,
  /**
   * Planned activity groups
   */
  PlannedActivityGroups: service_type_PlannedActivityGroups,
  /**
   * Workplace safety and/or accident prevention
   */
  WorkSafetyAccidentPrevention: service_type_WorkSafetyAccidentPrevention,
  /**
   * Financial assistance
   */
  FinancialAssistance: service_type_FinancialAssistance,
  /**
   * Financial information/advice
   */
  FinancialInformationAdvice: service_type_FinancialInformationAdvice,
  /**
   * Material aid
   */
  MaterialAid: service_type_MaterialAid,
  /**
   * General Practice/GP (doctor)
   */
  GeneralPractice: service_type_GeneralPractice,
  /**
   * Accommodation placement and/or support
   */
  AccommodationPlacementSupport: service_type_AccommodationPlacementSupport,
  /**
   * Crisis/emergency accommodation
   */
  CrisisEmergencyAccommodation: service_type_CrisisEmergencyAccommodation,
  /**
   * Homelessness support
   */
  HomelessnessSupport: service_type_HomelessnessSupport,
  /**
   * Housing information/referral
   */
  HousingInformationReferral: service_type_HousingInformationReferral,
  /**
   * Public rental housing
   */
  PublicRentalHousing: service_type_PublicRentalHousing,
  /**
   * Acupuncture
   */
  Acupuncture: service_type_Acupuncture,
  /**
   * Interpreting/Multilingual/Language service
   */
  InterpretingMultilingualService: service_type_InterpretingMultilingualService,
  /**
   * Juvenile Justice
   */
  JuvenileJustice: service_type_JuvenileJustice,
  /**
   * Legal advocacy
   */
  LegalAdvocacy: service_type_LegalAdvocacy,
  /**
   * Legal information/advice/referral
   */
  LegalInformationAdviceReferral: service_type_LegalInformationAdviceReferral,
  /**
   * Mental health advocacy
   */
  MentalHealthAdvocacy: service_type_MentalHealthAdvocacy,
  /**
   * Mental health assessment/triage/crisis response
   */
  MentalHealthAssessTriageCrisisResponse: service_type_MentalHealthAssessTriageCrisisResponse,
  /**
   * Mental health case management/continuing care
   */
  MentalHealthCaseManagement: service_type_MentalHealthCaseManagement,
  /**
   * Mental health information/referral
   */
  MentalHealthInformationReferral: service_type_MentalHealthInformationReferral,
  /**
   * Mental health inpatient services (hospital psychiatric unit) - requires referral
   */
  MentalHealthInpatientServices: service_type_MentalHealthInpatientServices,
  /**
   * Mental health non-residential rehabilitation
   */
  MentalHealthNonResidentialRehab: service_type_MentalHealthNonResidentialRehab,
  /**
   * Alexander technique therapy
   */
  AlexanderTechniqueTherapy: service_type_AlexanderTechniqueTherapy,
  /**
   * Mental health residential rehabilitation/community care unit
   */
  MentalHealthResidentialRehabCCU: service_type_MentalHealthResidentialRehabCCU,
  /**
   * Psychiatry (requires referral)
   */
  PsychiatryRequiresReferral: service_type_PsychiatryRequiresReferral,
  /**
   * Psychology
   */
  Psychology: service_type_Psychology,
  /**
   * Martial arts
   */
  MartialArts: service_type_MartialArts,
  /**
   * Personal fitness training
   */
  PersonalFitnessTraining: service_type_PersonalFitnessTraining,
  /**
   * Physical activity group
   */
  PhysicalActivityGroup: service_type_PhysicalActivityGroup,
  /**
   * Physical activity programs
   */
  PhysicalActivityPrograms: service_type_PhysicalActivityPrograms,
  /**
   * Physical fitness testing
   */
  PhysicalFitnessTesting: service_type_PhysicalFitnessTesting,
  /**
   * Pilates
   */
  Pilates: service_type_Pilates,
  /**
   * Self defence
   */
  SelfDefence: service_type_SelfDefence,
  /**
   * Aromatherapy
   */
  Aromatherapy: service_type_Aromatherapy,
  /**
   * Sporting club
   */
  SportingClub: service_type_SportingClub,
  /**
   * Yoga
   */
  Yoga: service_type_Yoga,
  /**
   * Food safety
   */
  FoodSafety: service_type_FoodSafety,
  /**
   * Health regulatory, inspection and/or certification
   */
  HealthRegulatoryInspectionCert: service_type_HealthRegulatoryInspectionCert,
  /**
   * Workplace health and/or safety inspection and/or certification
   */
  WorkHealthSafetyInspectionCert: service_type_WorkHealthSafetyInspectionCert,
  /**
   * Carer support
   */
  CarerSupport: service_type_CarerSupport,
  /**
   * Respite care
   */
  RespiteCare: service_type_RespiteCare,
  /**
   * Anatomical Pathology (including Cytopathology & Forensic Pathology)
   */
  AnatomicalPathology: service_type_AnatomicalPathology,
  /**
   * Pathology - Clinical Chemistry
   */
  PathologyClinicalChemistry: service_type_PathologyClinicalChemistry,
  /**
   * Pathology - General
   */
  PathologyGeneral: service_type_PathologyGeneral,
  /**
   * Biorhythm services
   */
  BiorhythmServices: service_type_BiorhythmServices,
  /**
   * Pathology - Genetics
   */
  PathologyGenetics: service_type_PathologyGenetics,
  /**
   * Pathology - Haematology
   */
  PathologyHaematology: service_type_PathologyHaematology,
  /**
   * Pathology - Immunology
   */
  PathologyImmunology: service_type_PathologyImmunology,
  /**
   * Pathology - Microbiology
   */
  PathologyMicrobiology: service_type_PathologyMicrobiology,
  /**
   * Anaesthesiology - Pain Medicine
   */
  AnaesthesiologyPainMedicine: service_type_AnaesthesiologyPainMedicine,
  /**
   * Cardiology
   */
  Cardiology: service_type_Cardiology,
  /**
   * Clinical Genetics
   */
  ClinicalGenetics: service_type_ClinicalGenetics,
  /**
   * Clinical Pharmacology
   */
  ClinicalPharmacology: service_type_ClinicalPharmacology,
  /**
   * Dermatology
   */
  Dermatology: service_type_Dermatology,
  /**
   * Endocrinology
   */
  Endocrinology: service_type_Endocrinology,
  /**
   * Bowen therapy
   */
  BowenTherapy: service_type_BowenTherapy,
  /**
   * Gastroenterology & Hepatology
   */
  GastroenterologyAndHepatology: service_type_GastroenterologyAndHepatology,
  /**
   * Geriatric medicine
   */
  GeriatricMedicine: service_type_GeriatricMedicine,
  /**
   * Immunology & Allergy
   */
  ImmunologyAndAllergy: service_type_ImmunologyAndAllergy,
  /**
   * Infectious diseases
   */
  InfectiousDiseases: service_type_InfectiousDiseases,
  /**
   * Intensive care medicine
   */
  IntensiveCareMedicine: service_type_IntensiveCareMedicine,
  /**
   * Medical Oncology
   */
  MedicalOncology: service_type_MedicalOncology,
  /**
   * Nephrology
   */
  Nephrology: service_type_Nephrology,
  /**
   * Neurology
   */
  Neurology: service_type_Neurology,
  /**
   * Occupational Medicine
   */
  OccupationalMedicine: service_type_OccupationalMedicine,
  /**
   * Palliative Medicine
   */
  PalliativeMedicine: service_type_PalliativeMedicine,
  /**
   * Chinese herbal medicine
   */
  ChineseHerbalMedicine: service_type_ChineseHerbalMedicine,
  /**
   * Public Health Medicine
   */
  PublicHealthMedicine: service_type_PublicHealthMedicine,
  /**
   * Rehabilitation Medicine
   */
  RehabilitationMedicine: service_type_RehabilitationMedicine,
  /**
   * Rheumatology
   */
  Rheumatology: service_type_Rheumatology,
  /**
   * Sleep Medicine
   */
  SleepMedicine: service_type_SleepMedicine,
  /**
   * Thoracic medicine
   */
  ThoracicMedicine: service_type_ThoracicMedicine,
  /**
   * Gynaecological Oncology
   */
  GynaecologicalOncology: service_type_GynaecologicalOncology,
  /**
   * Obstetrics & Gynaecology
   */
  ObstetricsAndGynaecology: service_type_ObstetricsAndGynaecology,
  /**
   * Reproductive Endocrinology & Infertility
   */
  ReproductiveEndocrinologyInfertility: service_type_ReproductiveEndocrinologyInfertility,
  /**
   * Urogynaecology
   */
  Urogynaecology: service_type_Urogynaecology,
  /**
   * Neonatology & Perinatology
   */
  NeonatologyAndPerinatology: service_type_NeonatologyAndPerinatology,
  /**
   * Feldenkrais
   */
  Feldenkrais: service_type_Feldenkrais,
  /**
   * Paediatric Cardiology
   */
  PaediatricCardiology: service_type_PaediatricCardiology,
  /**
   * Paediatric Clinical Genetics
   */
  PaediatricClinicalGenetics: service_type_PaediatricClinicalGenetics,
  /**
   * Paediatric Clinical Pharmacology
   */
  PaediatricClinicalPharmacology: service_type_PaediatricClinicalPharmacology,
  /**
   * Paediatric Endocrinology
   */
  PaediatricEndocrinology: service_type_PaediatricEndocrinology,
  /**
   * Paediatric Gastroenterology & Hepatology
   */
  PaedGastroenterologyHepatology: service_type_PaedGastroenterologyHepatology,
  /**
   * Paediatric Haematology
   */
  PaediatricHaematology: service_type_PaediatricHaematology,
  /**
   * Paediatric Immunology & Allergy
   */
  PaediatricImmunologyAndAllergy: service_type_PaediatricImmunologyAndAllergy,
  /**
   * Paediatric Infectious diseases
   */
  PaediatricInfectiousDiseases: service_type_PaediatricInfectiousDiseases,
  /**
   * Paediatric intensive care medicine
   */
  PaediatricIntensiveCareMedicine: service_type_PaediatricIntensiveCareMedicine,
  /**
   * Paediatric Medical Oncology
   */
  PaediatricMedicalOncology: service_type_PaediatricMedicalOncology,
  /**
   * Aged care assessment
   */
  AgedCareAssessment: service_type_AgedCareAssessment,
  /**
   * Homoeopathy
   */
  Homoeopathy: service_type_Homoeopathy,
  /**
   * Paediatric Medicine
   */
  PaediatricMedicine: service_type_PaediatricMedicine,
  /**
   * Paediatric Nephrology
   */
  PaediatricNephrology: service_type_PaediatricNephrology,
  /**
   * Paediatric Neurology
   */
  PaediatricNeurology: service_type_PaediatricNeurology,
  /**
   * Paediatric Nuclear Medicine
   */
  PaediatricNuclearMedicine: service_type_PaediatricNuclearMedicine,
  /**
   * Paediatric Rehabilitation Medicine
   */
  PaediatricRehabilitationMedicine: service_type_PaediatricRehabilitationMedicine,
  /**
   * Paediatric Rheumatology
   */
  PaediatricRheumatology: service_type_PaediatricRheumatology,
  /**
   * Paediatric Sleep Medicine
   */
  PaediatricSleepMedicine: service_type_PaediatricSleepMedicine,
  /**
   * Paediatric Surgery
   */
  PaediatricSurgery: service_type_PaediatricSurgery,
  /**
   * Paediatric Thoracic Medicine
   */
  PaediatricThoracicMedicine: service_type_PaediatricThoracicMedicine,
  /**
   * Diagnostic Radiology/Xray/CT/Fluoroscopy
   */
  DiagRadiologyXrayCTFluoroscopy: service_type_DiagRadiologyXrayCTFluoroscopy,
  /**
   * Hydrotherapy
   */
  Hydrotherapy: service_type_Hydrotherapy,
  /**
   * Diagnostic Ultrasound
   */
  DiagnosticUltrasound: service_type_DiagnosticUltrasound,
  /**
   * Magnetic Resonance Imaging (MRI)
   */
  MagneticResonanceImagingMRI: service_type_MagneticResonanceImagingMRI,
  /**
   * Nuclear Medicine
   */
  NuclearMedicine: service_type_NuclearMedicine,
  /**
   * Obstetric & Gynaecological Ultrasound
   */
  ObstetricGynaecologicalUltrasound: service_type_ObstetricGynaecologicalUltrasound,
  /**
   * Radiation oncology
   */
  RadiationOncology: service_type_RadiationOncology,
  /**
   * Cardiothoracic surgery
   */
  CardiothoracicSurgery: service_type_CardiothoracicSurgery,
  /**
   * Neurosurgery
   */
  Neurosurgery: service_type_Neurosurgery,
  /**
   * Ophthalmology
   */
  Ophthalmology: service_type_Ophthalmology,
  /**
   * Orthopaedic surgery
   */
  OrthopaedicSurgery: service_type_OrthopaedicSurgery,
  /**
   * Otolaryngology - Head & Neck Surgery
   */
  OtolaryngologyHeadAndNeckSurgery: service_type_OtolaryngologyHeadAndNeckSurgery,
  /**
   * Hypnotherapy
   */
  Hypnotherapy: service_type_Hypnotherapy,
  /**
   * Plastic & Reconstructive Surgery
   */
  PlasticAndReconstructiveSurgery: service_type_PlasticAndReconstructiveSurgery,
  /**
   * Surgery - General
   */
  SurgeryGeneral: service_type_SurgeryGeneral,
  /**
   * Urology
   */
  Urology: service_type_Urology,
  /**
   * Vascular surgery
   */
  VascularSurgery: service_type_VascularSurgery,
  /**
   * Support groups
   */
  SupportGroups: service_type_SupportGroups,
  /**
   * Air ambulance
   */
  AirAmbulance: service_type_AirAmbulance,
  /**
   * Ambulance
   */
  Ambulance: service_type_Ambulance,
  /**
   * Blood transport
   */
  BloodTransport: service_type_BloodTransport,
  /**
   * Community bus
   */
  CommunityBus: service_type_CommunityBus,
  /**
   * Flying doctor service
   */
  FlyingDoctorService: service_type_FlyingDoctorService,
  /**
   * Kinesiology
   */
  Kinesiology: service_type_Kinesiology,
  /**
   * Patient transport
   */
  PatientTransport: service_type_PatientTransport,
  /**
   * A&E
   */
  AAndE: service_type_AAndE,
  /**
   * A&EP
   */
  AAndEP: service_type_AAndEP,
  /**
   * Abuse
   */
  Abuse: service_type_Abuse,
  /**
   * ACAS
   */
  ACAS: service_type_ACAS,
  /**
   * Access
   */
  Access: service_type_Access,
  /**
   * Accident
   */
  Accident: service_type_Accident,
  /**
   * Acute Inpatient Service's
   */
  AcuteInpatientServ: service_type_AcuteInpatientServ,
  /**
   * Adult Day Programs
   */
  AdultDayPrograms: service_type_AdultDayPrograms,
  /**
   * Adult Mental Health Services
   */
  AdultMentalHealthServices: service_type_AdultMentalHealthServices,
  /**
   * Magnetic therapy
   */
  MagneticTherapy: service_type_MagneticTherapy,
  /**
   * Advice
   */
  Advice: service_type_Advice,
  /**
   * Advocacy
   */
  Advocacy: service_type_Advocacy,
  /**
   * Aged Persons Mental Health Residential Units
   */
  AgedPersonsMental: service_type_AgedPersonsMental,
  /**
   * Aged Persons Mental Health Services
   */
  AgedPersonsMental_2: service_type_AgedPersonsMental_2,
  /**
   * Aged Persons Mental Health Teams
   */
  AgedPersonsMental_3: service_type_AgedPersonsMental_3,
  /**
   * Aids
   */
  Aids: service_type_Aids,
  /**
   * Al-Anon
   */
  AlAnon: service_type_AlAnon,
  /**
   * Alcohol
   */
  Alcohol: service_type_Alcohol,
  /**
   * Al-Teen
   */
  AlTeen: service_type_AlTeen,
  /**
   * Antenatal
   */
  Antenatal: service_type_Antenatal,
  /**
   * Massage therapy
   */
  MassageTherapy: service_type_MassageTherapy,
  /**
   * Anxiety
   */
  Anxiety: service_type_Anxiety,
  /**
   * Arthritis
   */
  Arthritis: service_type_Arthritis,
  /**
   * Assessment
   */
  Assessment: service_type_Assessment,
  /**
   * Assistance
   */
  Assistance: service_type_Assistance,
  /**
   * Asthma
   */
  Asthma: service_type_Asthma,
  /**
   * ATSS
   */
  ATSS: service_type_ATSS,
  /**
   * Attendant Care
   */
  AttendantCare: service_type_AttendantCare,
  /**
   * Babies
   */
  Babies: service_type_Babies,
  /**
   * Bathroom Modification
   */
  BathroomModificatio: service_type_BathroomModificatio,
  /**
   * Behavior
   */
  Behavior: service_type_Behavior,
  /**
   * Meditation
   */
  Meditation: service_type_Meditation,
  /**
   * Behavior Intervention
   */
  BehaviorInterventi: service_type_BehaviorInterventi,
  /**
   * Bereavement
   */
  Bereavement: service_type_Bereavement,
  /**
   * Bipolar
   */
  Bipolar: service_type_Bipolar,
  /**
   * Birth
   */
  Birth: service_type_Birth,
  /**
   * Birth Control
   */
  BirthControl: service_type_BirthControl,
  /**
   * Birthing Options
   */
  BirthingOptions: service_type_BirthingOptions,
  /**
   * BIST
   */
  BIST: service_type_BIST,
  /**
   * Blood
   */
  Blood: service_type_Blood,
  /**
   * Bone
   */
  Bone: service_type_Bone,
  /**
   * Bowel
   */
  Bowel: service_type_Bowel,
  /**
   * Myotherapy
   */
  Myotherapy: service_type_Myotherapy,
  /**
   * Brain
   */
  Brain: service_type_Brain,
  /**
   * Breast Feeding
   */
  BreastFeeding: service_type_BreastFeeding,
  /**
   * Breast Screen
   */
  BreastScreen: service_type_BreastScreen,
  /**
   * Brokerage
   */
  Brokerage: service_type_Brokerage,
  /**
   * Cancer
   */
  Cancer: service_type_Cancer,
  /**
   * Cancer Support
   */
  CancerSupport: service_type_CancerSupport,
  /**
   * Cardiovascular Disease
   */
  CardiovascularDisea: service_type_CardiovascularDisea,
  /**
   * Care Packages
   */
  CarePackages: service_type_CarePackages,
  /**
   * Carer
   */
  Carer: service_type_Carer,
  /**
   * Case Management
   */
  CaseManagement: service_type_CaseManagement,
  /**
   * Naturopathy
   */
  Naturopathy: service_type_Naturopathy,
  /**
   * Casualty
   */
  Casualty: service_type_Casualty,
  /**
   * Centrelink
   */
  Centrelink: service_type_Centrelink,
  /**
   * Chemists
   */
  Chemists: service_type_Chemists,
  /**
   * Child And Adolescent Mental Health Services
   */
  ChildAndAdolescent: service_type_ChildAndAdolescent,
  /**
   * Child Care
   */
  ChildCare: service_type_ChildCare,
  /**
   * Child Services
   */
  ChildServices: service_type_ChildServices,
  /**
   * Children
   */
  Children: service_type_Children,
  /**
   * Children's Services
   */
  ChildrenQuoteSServices: service_type_ChildrenQuoteSServices,
  /**
   * Cholesterol
   */
  Cholesterol: service_type_Cholesterol,
  /**
   * Clothing
   */
  Clothing: service_type_Clothing,
  /**
   * Reflexology
   */
  Reflexology: service_type_Reflexology,
  /**
   * Community Based Accommodation
   */
  CommunityBasedAcco: service_type_CommunityBasedAcco,
  /**
   * Community Care Unit
   */
  CommunityCareUnit: service_type_CommunityCareUnit,
  /**
   * Community Child And Adolescent Mental Health Services
   */
  CommunityChildAnd: service_type_CommunityChildAnd,
  /**
   * Community Health
   */
  CommunityHealth: service_type_CommunityHealth,
  /**
   * Community Residential Unit
   */
  CommunityResidentia: service_type_CommunityResidentia,
  /**
   * Community Transport
   */
  CommunityTransport: service_type_CommunityTransport,
  /**
   * Companion Visiting
   */
  CompanionVisiting: service_type_CompanionVisiting,
  /**
   * Companionship
   */
  Companionship: service_type_Companionship,
  /**
   * Consumer Advice
   */
  ConsumerAdvice: service_type_ConsumerAdvice,
  /**
   * Consumer Issues
   */
  ConsumerIssues: service_type_ConsumerIssues,
  /**
   * Aged Care information/referral
   */
  AgedCareInformationReferral: service_type_AgedCareInformationReferral,
  /**
   * Reiki
   */
  Reiki: service_type_Reiki,
  /**
   * Continuing Care Services
   */
  ContinuingCareServ: service_type_ContinuingCareServ,
  /**
   * Contraception Information
   */
  ContraceptionInform: service_type_ContraceptionInform,
  /**
   * Coordinating Bodies
   */
  CoordinatingBodies: service_type_CoordinatingBodies,
  /**
   * Correctional Services
   */
  CorrectionalService: service_type_CorrectionalService,
  /**
   * Council Environmental Health
   */
  CouncilEnvironmenta: service_type_CouncilEnvironmenta,
  /**
   * Counselling
   */
  Counselling: service_type_Counselling,
  /**
   * Criminal
   */
  Criminal: service_type_Criminal,
  /**
   * Crises
   */
  Crises: service_type_Crises,
  /**
   * Crisis Assessment And Treatment Services (Cats)
   */
  CrisisAssessmentAn: service_type_CrisisAssessmentAn,
  /**
   * Crisis Assistance
   */
  CrisisAssistance: service_type_CrisisAssistance,
  /**
   * Relaxation therapy
   */
  RelaxationTherapy: service_type_RelaxationTherapy,
  /**
   * Crisis Refuge
   */
  CrisisRefuge: service_type_CrisisRefuge,
  /**
   * Day Program
   */
  DayProgram: service_type_DayProgram,
  /**
   * Deaf
   */
  Deaf: service_type_Deaf,
  /**
   * Dental Hygiene
   */
  DentalHygiene: service_type_DentalHygiene,
  /**
   * Dentistry
   */
  Dentistry: service_type_Dentistry,
  /**
   * Dentures
   */
  Dentures: service_type_Dentures,
  /**
   * Depression
   */
  Depression: service_type_Depression,
  /**
   * Detoxification
   */
  Detoxification: service_type_Detoxification,
  /**
   * Diabetes
   */
  Diabetes: service_type_Diabetes,
  /**
   * Diaphragm Fitting
   */
  DiaphragmFitting: service_type_DiaphragmFitting,
  /**
   * Shiatsu
   */
  Shiatsu: service_type_Shiatsu,
  /**
   * Dieticians
   */
  Dieticians: service_type_Dieticians,
  /**
   * Disabled Parking
   */
  DisabledParking: service_type_DisabledParking,
  /**
   * District Nursing
   */
  DistrictNursing: service_type_DistrictNursing,
  /**
   * Divorce
   */
  Divorce: service_type_Divorce,
  /**
   * Doctors
   */
  Doctors: service_type_Doctors,
  /**
   * Drink-Drive
   */
  DrinkDrive: service_type_DrinkDrive,
  /**
   * Dual Diagnosis Services
   */
  DualDiagnosisServi: service_type_DualDiagnosisServi,
  /**
   * Early Choice
   */
  EarlyChoice: service_type_EarlyChoice,
  /**
   * Eating Disorder
   */
  EatingDisorder: service_type_EatingDisorder,
  /**
   * Western herbal medicine
   */
  WesternHerbalMedicine: service_type_WesternHerbalMedicine,
  /**
   * Emergency Relief
   */
  EmergencyRelief: service_type_EmergencyRelief,
  /**
   * Employment And Training
   */
  EmploymentAndTrain: service_type_EmploymentAndTrain,
  /**
   * Environment
   */
  Environment: service_type_Environment,
  /**
   * Equipment
   */
  Equipment: service_type_Equipment,
  /**
   * Exercise
   */
  Exercise: service_type_Exercise,
  /**
   * Facility
   */
  Facility: service_type_Facility,
  /**
   * Family Choice
   */
  FamilyChoice: service_type_FamilyChoice,
  /**
   * Family Law
   */
  FamilyLaw: service_type_FamilyLaw,
  /**
   * Family Options
   */
  FamilyOptions: service_type_FamilyOptions,
  /**
   * Family Services
   */
  FamilyServices: service_type_FamilyServices,
  /**
   * Family day care
   */
  FamilyDayCare: service_type_FamilyDayCare,
  /**
   * FFYA
   */
  FFYA: service_type_FFYA,
  /**
   * Financial Aid
   */
  FinancialAid: service_type_FinancialAid,
  /**
   * Fitness
   */
  Fitness: service_type_Fitness,
  /**
   * Flexible Care Packages
   */
  FlexibleCarePackag: service_type_FlexibleCarePackag,
  /**
   * Food
   */
  Food: service_type_Food,
  /**
   * Food Vouchers
   */
  FoodVouchers: service_type_FoodVouchers,
  /**
   * Forensic Mental Health Services
   */
  ForensicMentalHeal: service_type_ForensicMentalHeal,
  /**
   * Futures
   */
  Futures: service_type_Futures,
  /**
   * Futures For Young Adults
   */
  FuturesForYoungAd: service_type_FuturesForYoungAd,
  /**
   * General Practitioners
   */
  GeneralPractitioner: service_type_GeneralPractitioner,
  /**
   * Holiday programs
   */
  HolidayPrograms: service_type_HolidayPrograms,
  /**
   * Grants
   */
  Grants: service_type_Grants,
  /**
   * Grief
   */
  Grief: service_type_Grief,
  /**
   * Grief Counselling
   */
  GriefCounselling: service_type_GriefCounselling,
  /**
   * HACC
   */
  HACC: service_type_HACC,
  /**
   * Heart Disease
   */
  HeartDisease: service_type_HeartDisease,
  /**
   * Help
   */
  Help: service_type_Help,
  /**
   * High Blood Pressure
   */
  HighBloodPressure: service_type_HighBloodPressure,
  /**
   * Home Help
   */
  HomeHelp: service_type_HomeHelp,
  /**
   * Home Nursing
   */
  HomeNursing: service_type_HomeNursing,
  /**
   * Homefirst
   */
  Homefirst: service_type_Homefirst,
  /**
   * Kindergarten inclusion support for children with a disability
   */
  KindergartenInclusionSupport: service_type_KindergartenInclusionSupport,
  /**
   * Hospice Care
   */
  HospiceCare: service_type_HospiceCare,
  /**
   * Hospital Services
   */
  HospitalServices: service_type_HospitalServices,
  /**
   * Hospital To Home
   */
  HospitalToHome: service_type_HospitalToHome,
  /**
   * Hostel
   */
  Hostel: service_type_Hostel,
  /**
   * Hostel Accommodation
   */
  HostelAccommodation: service_type_HostelAccommodation,
  /**
   * Household Items
   */
  HouseholdItems: service_type_HouseholdItems,
  /**
   * Hypertension
   */
  Hypertension: service_type_Hypertension,
  /**
   * Illness
   */
  Illness: service_type_Illness,
  /**
   * Independent Living
   */
  IndependentLiving: service_type_IndependentLiving,
  /**
   * Kindergarten/preschool
   */
  KindergartenPreschool: service_type_KindergartenPreschool,
  /**
   * Information
   */
  Information: service_type_Information,
  /**
   * Injury
   */
  Injury: service_type_Injury,
  /**
   * Intake
   */
  Intake: service_type_Intake,
  /**
   * Intensive Mobile Youth Outreach Services (Imyos)
   */
  IntensiveMobileYou: service_type_IntensiveMobileYou,
  /**
   * Intervention
   */
  Intervention: service_type_Intervention,
  /**
   * Job Searching
   */
  JobSearching: service_type_JobSearching,
  /**
   * Justice
   */
  Justice: service_type_Justice,
  /**
   * Leisure
   */
  Leisure: service_type_Leisure,
  /**
   * Loans
   */
  Loans: service_type_Loans,
  /**
   * Low Income Earners
   */
  LowIncomeEarners: service_type_LowIncomeEarners,
  /**
   * Long day child care
   */
  LongDayChildCare: service_type_LongDayChildCare,
  /**
   * Lung
   */
  Lung: service_type_Lung,
  /**
   * Making A Difference
   */
  MakingADifference: service_type_MakingADifference,
  /**
   * Medical Services
   */
  MedicalServices: service_type_MedicalServices,
  /**
   * Medical Specialists
   */
  MedicalSpecialists: service_type_MedicalSpecialists,
  /**
   * Medication Administration
   */
  MedicationAdministr: service_type_MedicationAdministr,
  /**
   * Menstrual Information
   */
  MenstrualInformatio: service_type_MenstrualInformatio,
  /**
   * Methadone
   */
  Methadone: service_type_Methadone,
  /**
   * Mobile Support And Treatment Services (MSTS)
   */
  MobileSupportAndT: service_type_MobileSupportAndT,
  /**
   * Motor Neurone
   */
  MotorNeurone: service_type_MotorNeurone,
  /**
   * Multiple Sclerosis
   */
  MultipleSclerosis: service_type_MultipleSclerosis,
  /**
   * Occasional child care
   */
  OccasionalChildCare: service_type_OccasionalChildCare,
  /**
   * Neighbourhood House
   */
  NeighbourhoodHouse: service_type_NeighbourhoodHouse,
  /**
   * Nursing Home
   */
  NursingHome: service_type_NursingHome,
  /**
   * Nursing Mothers
   */
  NursingMothers: service_type_NursingMothers,
  /**
   * Obesity
   */
  Obesity: service_type_Obesity,
  /**
   * Occupational Health & Safety
   */
  OccupationalHealth: service_type_OccupationalHealth,
  /**
   * Optometrist
   */
  Optometrist: service_type_Optometrist,
  /**
   * Oral Hygiene
   */
  OralHygiene: service_type_OralHygiene,
  /**
   * Outpatients
   */
  Outpatients: service_type_Outpatients,
  /**
   * Outreach Service
   */
  OutreachService: service_type_OutreachService,
  /**
   * PADP
   */
  PADP: service_type_PADP,
  /**
   * Aged Residential Care
   */
  AgedResidentialCare: service_type_AgedResidentialCare,
  /**
   * Outside school hours care
   */
  OutsideSchoolHoursCare: service_type_OutsideSchoolHoursCare,
  /**
   * Pain
   */
  Pain: service_type_Pain,
  /**
   * Pap Smear
   */
  PapSmear: service_type_PapSmear,
  /**
   * Parenting
   */
  Parenting: service_type_Parenting,
  /**
   * Peak Organizations
   */
  PeakOrganizations: service_type_PeakOrganizations,
  /**
   * Personal Care
   */
  PersonalCare: service_type_PersonalCare,
  /**
   * Pharmacies
   */
  Pharmacies: service_type_Pharmacies,
  /**
   * Phobias
   */
  Phobias: service_type_Phobias,
  /**
   * Physical
   */
  Physical: service_type_Physical,
  /**
   * Physical Activity
   */
  PhysicalActivity: service_type_PhysicalActivity,
  /**
   * Postnatal
   */
  Postnatal: service_type_Postnatal,
  /**
   * Children's play programs
   */
  ChildrenQuoteSPlayPrograms: service_type_ChildrenQuoteSPlayPrograms,
  /**
   * Pregnancy
   */
  Pregnancy: service_type_Pregnancy,
  /**
   * Pregnancy Tests
   */
  PregnancyTests: service_type_PregnancyTests,
  /**
   * Preschool
   */
  Preschool: service_type_Preschool,
  /**
   * Prescriptions
   */
  Prescriptions: service_type_Prescriptions,
  /**
   * Primary Mental Health And Early Intervention Teams
   */
  PrimaryMentalHealt: service_type_PrimaryMentalHealt,
  /**
   * Property Maintenance
   */
  PropertyMaintenance: service_type_PropertyMaintenance,
  /**
   * Prostate
   */
  Prostate: service_type_Prostate,
  /**
   * Psychiatric
   */
  Psychiatric: service_type_Psychiatric,
  /**
   * Psychiatric Disability Support Services - Home-Based Outreach
   */
  PsychiatricDisabili: service_type_PsychiatricDisabili,
  /**
   * Psychiatric Disability Support Services - Planned Respite
   */
  PsychiatricDisabili_2: service_type_PsychiatricDisabili_2,
  /**
   * Parenting & family management support/education
   */
  ParentingFamilySupportEducation: service_type_ParentingFamilySupportEducation,
  /**
   * Psychiatric Disability Support Services - Residential Rehabilitation
   */
  PsychiatricDisabili_3: service_type_PsychiatricDisabili_3,
  /**
   * Psychiatric Disability Support Services Home-Based Outreach
   */
  PsychiatricDisabili_4: service_type_PsychiatricDisabili_4,
  /**
   * Psychiatric Disability Support Services Mutual Support And Self Help
   */
  PsychiatricDisabili_5: service_type_PsychiatricDisabili_5,
  /**
   * Psychiatric Support
   */
  PsychiatricSupport: service_type_PsychiatricSupport,
  /**
   * Recreation
   */
  Recreation: service_type_Recreation,
  /**
   * Referral
   */
  Referral: service_type_Referral,
  /**
   * Refuge
   */
  Refuge: service_type_Refuge,
  /**
   * Rent Assistance
   */
  RentAssistance: service_type_RentAssistance,
  /**
   * Residential Facilities
   */
  ResidentialFaciliti: service_type_ResidentialFaciliti,
  /**
   * Residential Respite
   */
  ResidentialRespite: service_type_ResidentialRespite,
  /**
   * Playgroup
   */
  Playgroup: service_type_Playgroup,
  /**
   * Respiratory
   */
  Respiratory: service_type_Respiratory,
  /**
   * Response
   */
  Response: service_type_Response,
  /**
   * Rooming Houses
   */
  RoomingHouses: service_type_RoomingHouses,
  /**
   * Safe Sex
   */
  SafeSex: service_type_SafeSex,
  /**
   * Secure Extended Care Inpatient Services
   */
  SecureExtendedCare: service_type_SecureExtendedCare,
  /**
   * Self Help
   */
  SelfHelp: service_type_SelfHelp,
  /**
   * Separation
   */
  Separation: service_type_Separation,
  /**
   * Services
   */
  Services: service_type_Services,
  /**
   * Sex Education
   */
  SexEducation: service_type_SexEducation,
  /**
   * Sexual Abuse
   */
  SexualAbuse: service_type_SexualAbuse,
  /**
   * School nursing
   */
  SchoolNursing: service_type_SchoolNursing,
  /**
   * Sexual Issues
   */
  SexualIssues: service_type_SexualIssues,
  /**
   * Sexually Transmitted Diseases
   */
  SexuallyTransmitted: service_type_SexuallyTransmitted,
  /**
   * SIDS
   */
  SIDS: service_type_SIDS,
  /**
   * Social Support
   */
  SocialSupport: service_type_SocialSupport,
  /**
   * Socialisation
   */
  Socialisation: service_type_Socialisation,
  /**
   * Special Needs
   */
  SpecialNeeds: service_type_SpecialNeeds,
  /**
   * Speech Therapist
   */
  SpeechTherapist: service_type_SpeechTherapist,
  /**
   * Splinting
   */
  Splinting: service_type_Splinting,
  /**
   * Sport
   */
  Sport: service_type_Sport,
  /**
   * Statewide And Specialist Services
   */
  StatewideAndSpecia: service_type_StatewideAndSpecia,
  /**
   * Toy library
   */
  ToyLibrary: service_type_ToyLibrary,
  /**
   * STD
   */
  STD: service_type_STD,
  /**
   * STI
   */
  STI: service_type_STI,
  /**
   * Stillbirth
   */
  Stillbirth: service_type_Stillbirth,
  /**
   * Stomal Care
   */
  StomalCare: service_type_StomalCare,
  /**
   * Stroke
   */
  Stroke: service_type_Stroke,
  /**
   * Substance Abuse
   */
  SubstanceAbuse: service_type_SubstanceAbuse,
  /**
   * Support
   */
  Support: service_type_Support,
  /**
   * Syringes
   */
  Syringes: service_type_Syringes,
  /**
   * Teeth
   */
  Teeth: service_type_Teeth,
  /**
   * Tenancy Advice
   */
  TenancyAdvice: service_type_TenancyAdvice,
  /**
   * Child protection/child abuse report
   */
  ChildProtectionChildAbuseReport: service_type_ChildProtectionChildAbuseReport,
  /**
   * Terminal Illness
   */
  TerminalIllness: service_type_TerminalIllness,
  /**
   * Therapy
   */
  Therapy: service_type_Therapy,
  /**
   * Transcription
   */
  Transcription: service_type_Transcription,
  /**
   * Translating Services
   */
  TranslatingServices: service_type_TranslatingServices,
  /**
   * Translator
   */
  Translator: service_type_Translator,
  /**
   * Transport
   */
  Transport: service_type_Transport,
  /**
   * Vertebrae
   */
  Vertebrae: service_type_Vertebrae,
  /**
   * Violence
   */
  Violence: service_type_Violence,
  /**
   * Vocational Guidance
   */
  VocationalGuidance: service_type_VocationalGuidance,
  /**
   * Weight
   */
  Weight: service_type_Weight,
  /**
   * Foster care
   */
  FosterCare: service_type_FosterCare,
  /**
   * Welfare Assistance
   */
  WelfareAssistance: service_type_WelfareAssistance,
  /**
   * Welfare Counselling
   */
  WelfareCounselling: service_type_WelfareCounselling,
  /**
   * Wheelchairs
   */
  Wheelchairs: service_type_Wheelchairs,
  /**
   * Wound Management
   */
  WoundManagement: service_type_WoundManagement,
  /**
   * Young People At Risk
   */
  YoungPeopleAtRisk: service_type_YoungPeopleAtRisk,
  /**
   * Further Description - Community Health Care
   */
  FurtherDescCommunityHealthCare: service_type_FurtherDescCommunityHealthCare,
  /**
   * Library
   */
  Library: service_type_Library,
  /**
   * Community Hours
   */
  CommunityHours: service_type_CommunityHours,
  /**
   * Further Description - Specialist Medical
   */
  FurtherDescSpecialistMedical: service_type_FurtherDescSpecialistMedical,
  /**
   * Hepatology
   */
  Hepatology: service_type_Hepatology,
  /**
   * Residential/ out of home care
   */
  ResidentialOutOfHomeCare: service_type_ResidentialOutOfHomeCare,
  /**
   * Gastroenterology
   */
  Gastroenterology: service_type_Gastroenterology,
  /**
   * Gynaecology
   */
  Gynaecology: service_type_Gynaecology,
  /**
   * Obstetrics
   */
  Obstetrics: service_type_Obstetrics,
  /**
   * Further Description - Specialist Surgical
   */
  FurtherDescSpecialistSurgical: service_type_FurtherDescSpecialistSurgical,
  /**
   * Placement Protection
   */
  PlacementProtection: service_type_PlacementProtection,
  /**
   * Family Violence
   */
  FamilyViolence: service_type_FamilyViolence,
  /**
   * Integrated Family Services
   */
  IntegratedFamilyServices: service_type_IntegratedFamilyServices,
  /**
   * Diabetes Educator
   */
  DiabetesEducator: service_type_DiabetesEducator,
  /**
   * Kinship Care
   */
  KinshipCare: service_type_KinshipCare,
  /**
   * Support for young people leaving care
   */
  SupportYoungPeopleLeavingCare: service_type_SupportYoungPeopleLeavingCare,
  /**
   * General Mental Health Services
   */
  GeneralMentalHealthServices: service_type_GeneralMentalHealthServices,
  /**
   * Exercise Physiology
   */
  ExercisePhysiology: service_type_ExercisePhysiology,
  /**
   * Medical Research
   */
  MedicalResearch: service_type_MedicalResearch,
  /**
   * Youth
   */
  Youth: service_type_Youth,
  /**
   * Youth Services
   */
  YouthServices: service_type_YouthServices,
  /**
   * Youth Health
   */
  YouthHealth: service_type_YouthHealth,
  /**
   * Child and Family Services
   */
  ChildAndFamilySer: service_type_ChildAndFamilySer,
  /**
   * Home Visits
   */
  HomeVisits: service_type_HomeVisits,
  /**
   * Mobile Services
   */
  MobileServices: service_type_MobileServices,
  /**
   * Case management for older persons
   */
  CaseManagementForOlderPersons: service_type_CaseManagementForOlderPersons,
  /**
   * Audiology
   */
  Audiology: service_type_Audiology,
  /**
   * Before and/or After School Care
   */
  BeforeAndOrAfter: service_type_BeforeAndOrAfter,
  /**
   * Cancer Services
   */
  CancerServices: service_type_CancerServices,
  /**
   * Integrated Cancer Services
   */
  IntegratedCancerSe: service_type_IntegratedCancerSe,
  /**
   * Multidisciplinary Services
   */
  MultidisciplinarySe: service_type_MultidisciplinarySe,
  /**
   * Multidisciplinary Cancer Services
   */
  MultidisciplinaryCa: service_type_MultidisciplinaryCa,
  /**
   * Meetings
   */
  Meetings: service_type_Meetings,
  /**
   * Blood pressure monitoring
   */
  BloodPressureMonit: service_type_BloodPressureMonit,
  /**
   * Dose administration aid
   */
  DoseAdministration: service_type_DoseAdministration,
  /**
   * Medical Equipment Hire
   */
  MedicalEquipmentHi: service_type_MedicalEquipmentHi,
  /**
   * Parenting & family support/education
   */
  ParentingFamilySupportEducation_2: service_type_ParentingFamilySupportEducation_2,
  /**
   * Blood donation
   */
  BloodDonation: service_type_BloodDonation,
  /**
   * Deputising Service
   */
  DeputisingService: service_type_DeputisingService,
  /**
   * Cancer Support Groups
   */
  CancerSupportGroups: service_type_CancerSupportGroups,
  /**
   * Community Cancer Services
   */
  CommunityCancerServices: service_type_CommunityCancerServices,
  /**
   * Chiropractic
   */
  Chiropractic: service_type_Chiropractic,
  /**
   * Dietetics
   */
  Dietetics: service_type_Dietetics,
  /**
   * Disability Care Transport
   */
  DisabilityCareTransport: service_type_DisabilityCareTransport,
  /**
   * Aged Care Transport
   */
  AgedCareTransport: service_type_AgedCareTransport,
  /**
   * Diabetes Education service
   */
  DiabetesEducationS: service_type_DiabetesEducationS,
  /**
   * Cardiac Rehabilitation Service
   */
  CardiacRehabilitati: service_type_CardiacRehabilitati,
  /**
   * Young Adult Diabetes services (YADS)
   */
  YoungAdultDiabetes: service_type_YoungAdultDiabetes,
  /**
   * Pulmonary Rehabilitation Service
   */
  PulmonaryRehabilita: service_type_PulmonaryRehabilita,
  /**
   * Art therapy
   */
  ArtTherapy: service_type_ArtTherapy,
  /**
   * Medication Reviews
   */
  MedicationReviews: service_type_MedicationReviews,
  /**
   * Telephone Counselling
   */
  TelephoneCounselling: service_type_TelephoneCounselling,
  /**
   * Telephone Help Line
   */
  TelephoneHelpLine: service_type_TelephoneHelpLine,
  /**
   * Family planning
   */
  FamilyPlanning: service_type_FamilyPlanning,
  /**
   * Online Service
   */
  OnlineService: service_type_OnlineService,
  /**
   * Crisis - Mental Health
   */
  CrisisMentalHealth: service_type_CrisisMentalHealth,
  /**
   * Youth Crisis
   */
  YouthCrisis: service_type_YouthCrisis,
  /**
   * Sexual Assault
   */
  SexualAssault: service_type_SexualAssault,
  /**
   * GPAH Other
   */
  GPAHOther: service_type_GPAHOther,
  /**
   * Paediatric Dermatology
   */
  PaediatricDermatology: service_type_PaediatricDermatology,
  /**
   * Veterans Services
   */
  VeteransServices: service_type_VeteransServices,
  /**
   * Veterans
   */
  Veterans: service_type_Veterans,
  /**
   * Food Relief/food/meals
   */
  FoodReliefFoodMeals: service_type_FoodReliefFoodMeals,
  /**
   * Health advocacy/Liaison service
   */
  HealthAdvocacyLiaisonService: service_type_HealthAdvocacyLiaisonService,
  /**
   * Dementia Care
   */
  DementiaCare: service_type_DementiaCare,
  /**
   * Alzheimer
   */
  Alzheimer: service_type_Alzheimer,
  /**
   * Drug and/or alcohol support groups
   */
  DrugAndOrAlcoholSupportGroups: service_type_DrugAndOrAlcoholSupportGroups,
  /**
   * One on One Support/Mentoring/Coaching
   */
  VAL1On1SupportMentoringCoaching: service_type_VAL1On1SupportMentoringCoaching,
  /**
   * Chronic Disease Management
   */
  ChronicDiseaseManagement: service_type_ChronicDiseaseManagement,
  /**
   * Liaison Services
   */
  LiaisonServices: service_type_LiaisonServices,
  /**
   * Walk in Centre / non emergency
   */
  WalkInCentreNonEmergency: service_type_WalkInCentreNonEmergency,
  /**
   * Inpatients
   */
  Inpatients: service_type_Inpatients,
  /**
   * Spiritual Counselling
   */
  SpiritualCounselling: service_type_SpiritualCounselling,
  /**
   * Women's Health
   */
  WomenQuoteSHealth: service_type_WomenQuoteSHealth,
  /**
   * Health information/referral
   */
  HealthInformationReferral: service_type_HealthInformationReferral,
  /**
   * Men's Health
   */
  MenQuoteSHealth: service_type_MenQuoteSHealth,
  /**
   * Health education/Health awareness program
   */
  HealthEducationAwarenessProgram: service_type_HealthEducationAwarenessProgram,
  /**
   * Test Message
   */
  TestMessage: service_type_TestMessage,
  /**
   * Remedial Massage
   */
  RemedialMassage: service_type_RemedialMassage,
  /**
   * Adolescent Mental Health Services
   */
  AdolescentMentalHealthServices: service_type_AdolescentMentalHealthServices,
  /**
   * Youth drop in/assistance/support
   */
  YouthDropInAssistanceSupport: service_type_YouthDropInAssistanceSupport,
  /**
   * Aboriginal Health Worker
   */
  AboriginalHealthWorker: service_type_AboriginalHealthWorker,
  /**
   * Women's Health Clinic
   */
  WomenQuoteSHealthClinic: service_type_WomenQuoteSHealthClinic,
  /**
   * Men's Health Clinic
   */
  MenQuoteSHealthClinic: service_type_MenQuoteSHealthClinic,
  /**
   * Migrant Health Clinic
   */
  MigrantHealthClinic: service_type_MigrantHealthClinic,
  /**
   * Immunization
   */
  Immunization: service_type_Immunization,
  /**
   * Refugee Health Clinic
   */
  RefugeeHealthClinic: service_type_RefugeeHealthClinic,
  /**
   * Aboriginal Health Clinic
   */
  AboriginalHealthClinic: service_type_AboriginalHealthClinic,
  /**
   * Nurse Practitioner lead Clinic/s
   */
  NursePractitionerLeadClinicS: service_type_NursePractitionerLeadClinicS,
  /**
   * Nurse lead Clinic/s
   */
  NurseLeadClinicS: service_type_NurseLeadClinicS,
  /**
   * Culturally tailored support groups
   */
  CulturallyTailoredSupportGroups: service_type_CulturallyTailoredSupportGroups,
  /**
   * Culturally tailored health promotion
   */
  CulturallyTailoredHealthPromotion: service_type_CulturallyTailoredHealthPromotion,
  /**
   * Rehabilitation
   */
  Rehabilitation: service_type_Rehabilitation,
  /**
   * Education information/referral
   */
  EducationInformationReferral: service_type_EducationInformationReferral,
  /**
   * Maternal & child health
   */
  MaternalAndChildHealth: service_type_MaternalAndChildHealth,
  /**
   * Social Work
   */
  SocialWork: service_type_SocialWork,
  /**
   * Haematology
   */
  Haematology: service_type_Haematology,
  /**
   * Maternity Shared Care
   */
  MaternitySharedCar: service_type_MaternitySharedCar,
  /**
   * Rehabilitation Service
   */
  RehabilitationServi: service_type_RehabilitationServi,
  /**
   * Cranio-Sacral Therapy
   */
  CranioSacralTherapy: service_type_CranioSacralTherapy,
  /**
   * Prosthetics & Orthotics
   */
  ProstheticsAndOrthotics: service_type_ProstheticsAndOrthotics,
  /**
   * Home Medicine Review
   */
  HomeMedicineReview: service_type_HomeMedicineReview,
  /**
   * Nursing
   */
  Nursing: service_type_Nursing,
  /**
   * GPAH - Medical
   */
  GPAHMedical: service_type_GPAHMedical,
  /**
   * Music Therapy
   */
  MusicTherapy: service_type_MusicTherapy,
  /**
   * Falls Prevention
   */
  FallsPrevention: service_type_FallsPrevention,
  /**
   * Accommodation/Tenancy
   */
  AccommodationTenancy: service_type_AccommodationTenancy,
  /**
   * Delivered meals (meals on wheels)
   */
  DeliveredMealsMealsOnWheels: service_type_DeliveredMealsMealsOnWheels,
  /**
   * Nutrition
   */
  Nutrition: service_type_Nutrition,
  /**
   * Assess-Skill, Ability, Needs
   */
  AssessSkillAbilityNeeds: service_type_AssessSkillAbilityNeeds,
  /**
   * Assist Access/Maintain Employ
   */
  AssistAccessMaintainEmploy: service_type_AssistAccessMaintainEmploy,
  /**
   * Assist Prod-Pers Care/Safety
   */
  AssistProdPersCareSafety: service_type_AssistProdPersCareSafety,
  /**
   * Assist-Integrate School/Ed
   */
  AssistIntegrateSchoolEd: service_type_AssistIntegrateSchoolEd,
  /**
   * Assist-Life Stage, Transition
   */
  AssistLifeStageTransition: service_type_AssistLifeStageTransition,
  /**
   * Assist-Personal Activities
   */
  AssistPersonalActivities: service_type_AssistPersonalActivities,
  /**
   * Assist-Travel/Transport
   */
  AssistTravelTransport: service_type_AssistTravelTransport,
  /**
   * Assistive Equip-General Tasks
   */
  AssistiveEquipGeneralTasks: service_type_AssistiveEquipGeneralTasks,
  /**
   * Assistive Equip-Recreation
   */
  AssistiveEquipRecreation: service_type_AssistiveEquipRecreation,
  /**
   * Assistive Prod-Household Task
   */
  AssistiveProdHouseholdTask: service_type_AssistiveProdHouseholdTask,
  /**
   * Occupational therapy
   */
  OccupationalTherapy: service_type_OccupationalTherapy,
  /**
   * Behavior Support
   */
  BehaviorSupport: service_type_BehaviorSupport,
  /**
   * Comms & Info Equipment
   */
  CommsAndInfoEquipment: service_type_CommsAndInfoEquipment,
  /**
   * Community Nursing Care
   */
  CommunityNursingCare: service_type_CommunityNursingCare,
  /**
   * Daily Tasks/Shared Living
   */
  DailyTasksSharedLiving: service_type_DailyTasksSharedLiving,
  /**
   * Development-Life Skills
   */
  DevelopmentLifeSkills: service_type_DevelopmentLifeSkills,
  /**
   * Early Childhood Supports
   */
  EarlyChildhoodSupports: service_type_EarlyChildhoodSupports,
  /**
   * Equipment Special Assess Setup
   */
  EquipmentSpecialAssessSetup: service_type_EquipmentSpecialAssessSetup,
  /**
   * Hearing Equipment
   */
  HearingEquipment: service_type_HearingEquipment,
  /**
   * Home Modification
   */
  HomeModification: service_type_HomeModification,
  /**
   * Household Tasks
   */
  HouseholdTasks: service_type_HouseholdTasks,
  /**
   * Optometry
   */
  Optometry: service_type_Optometry,
  /**
   * Interpret/Translate
   */
  InterpretTranslate: service_type_InterpretTranslate,
  /**
   * Other Innovative Supports
   */
  OtherInnovativeSupports: service_type_OtherInnovativeSupports,
  /**
   * Participate Community
   */
  ParticipateCommunity: service_type_ParticipateCommunity,
  /**
   * Personal Mobility Equipment
   */
  PersonalMobilityEquipment: service_type_PersonalMobilityEquipment,
  /**
   * Physical Wellbeing
   */
  PhysicalWellbeing: service_type_PhysicalWellbeing,
  /**
   * Plan Management
   */
  PlanManagement: service_type_PlanManagement,
  /**
   * Therapeutic Supports
   */
  TherapeuticSupports: service_type_TherapeuticSupports,
  /**
   * Training-Travel Independence
   */
  TrainingTravelIndependence: service_type_TrainingTravelIndependence,
  /**
   * Vehicle modifications
   */
  VehicleModifications: service_type_VehicleModifications,
  /**
   * Vision Equipment
   */
  VisionEquipment: service_type_VisionEquipment,
  /**
   * Osteopathy
   */
  Osteopathy: service_type_Osteopathy,
  /**
   * Pharmacy
   */
  Pharmacy: service_type_Pharmacy,
  /**
   * Physiotherapy
   */
  Physiotherapy: service_type_Physiotherapy,
  /**
   * Podiatry
   */
  Podiatry: service_type_Podiatry,
  /**
   * Sexual health
   */
  SexualHealth: service_type_SexualHealth,
  /**
   * Speech pathology/therapy
   */
  SpeechPathologyTherapy: service_type_SpeechPathologyTherapy,
  /**
   * Bereavement counselling
   */
  BereavementCounselling: service_type_BereavementCounselling,
  /**
   * Friendly visiting
   */
  FriendlyVisiting: service_type_FriendlyVisiting,
  /**
   * Crisis counselling
   */
  CrisisCounselling: service_type_CrisisCounselling,
  /**
   * Family counselling and/or family therapy
   */
  FamilyCounsellingTherapy: service_type_FamilyCounsellingTherapy,
  /**
   * Family violence counselling
   */
  FamilyViolenceCounselling: service_type_FamilyViolenceCounselling,
  /**
   * Financial counselling
   */
  FinancialCounselling: service_type_FinancialCounselling,
  /**
   * Generalist counselling
   */
  GeneralistCounselling: service_type_GeneralistCounselling,
  /**
   * Genetic counselling
   */
  GeneticCounselling: service_type_GeneticCounselling,
  /**
   * Health counselling
   */
  HealthCounselling: service_type_HealthCounselling,
  /**
   * Mediation
   */
  Mediation: service_type_Mediation,
  /**
   * Problem gambling counselling
   */
  ProblemGamblingCounselling: service_type_ProblemGamblingCounselling,
  /**
   * Relationship counselling
   */
  RelationshipCounselling: service_type_RelationshipCounselling,
  /**
   * Home care/housekeeping assistance
   */
  HomeCareHousekeepingAssistance: service_type_HomeCareHousekeepingAssistance,
  /**
   * Sexual assault counselling
   */
  SexualAssaultCounselling: service_type_SexualAssaultCounselling,
  /**
   * Trauma counselling
   */
  TraumaCounselling: service_type_TraumaCounselling,
  /**
   * Victims of crime counselling
   */
  VictimsOfCrimeCounselling: service_type_VictimsOfCrimeCounselling,
  /**
   * Cemetery operation
   */
  CemeteryOperation: service_type_CemeteryOperation,
  /**
   * Cremation
   */
  Cremation: service_type_Cremation,
  /**
   * Death service information
   */
  DeathServiceInformation: service_type_DeathServiceInformation,
  /**
   * Funeral services
   */
  FuneralServices: service_type_FuneralServices,
  /**
   * Endodontic
   */
  Endodontic: service_type_Endodontic,
  /**
   * General dental
   */
  GeneralDental: service_type_GeneralDental,
  /**
   * Oral medicine
   */
  OralMedicine: service_type_OralMedicine,
  /**
   * Home maintenance and repair
   */
  HomeMaintenanceAndRepair: service_type_HomeMaintenanceAndRepair,
  /**
   * Oral surgery
   */
  OralSurgery: service_type_OralSurgery,
  /**
   * Orthodontic
   */
  Orthodontic: service_type_Orthodontic,
  /**
   * Paediatric Dentistry
   */
  PaediatricDentistry: service_type_PaediatricDentistry,
  /**
   * Periodontic
   */
  Periodontic: service_type_Periodontic,
  /**
   * Prosthodontic
   */
  Prosthodontic: service_type_Prosthodontic,
  /**
   * Acquired brain injury information/referral
   */
  AcquiredBrainInjuryInfoReferral: service_type_AcquiredBrainInjuryInfoReferral,
  /**
   * Disability advocacy
   */
  DisabilityAdvocacy: service_type_DisabilityAdvocacy,
  /**
   * Disability aids & equipment
   */
  DisabilityAidsAndEquipment: service_type_DisabilityAidsAndEquipment,
  /**
   * Disability case management
   */
  DisabilityCaseManagement: service_type_DisabilityCaseManagement,
  /**
   * Disability day programs & activities
   */
  DisabilityDayProgramsActivities: service_type_DisabilityDayProgramsActivities,
};
const service_uscls_ExamCompPrimary: Coding = {
  code: '1101',
  display: 'Exam, comp, primary',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_ExamCompMixed: Coding = {
  code: '1102',
  display: 'Exam, comp, mixed',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_ExamCompPermanent: Coding = {
  code: '1103',
  display: 'Exam, comp, permanent',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_Polishing1Unit: Coding = {
  code: '11101',
  display: 'Polishing, 1 unit',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_Polishing2Unit: Coding = {
  code: '11102',
  display: 'Polishing, 2 unit',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_Polishing3Unit: Coding = {
  code: '11103',
  display: 'Polishing, 3 unit',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_Polishing4Unit: Coding = {
  code: '11104',
  display: 'Polishing, 4 unit',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_ExamRecall: Coding = {
  code: '1201',
  display: 'Exam, recall',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_ExamEmergency: Coding = {
  code: '1205',
  display: 'Exam, emergency',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_RadiographSeries12: Coding = {
  code: '2101',
  display: 'Radiograph, series (12)',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_RadiographSeries16: Coding = {
  code: '2102',
  display: 'Radiograph, series (16)',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_Amalgam1Surface: Coding = {
  code: '21211',
  display: 'Amalgam, 1 surface',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_Amalgam2Surface: Coding = {
  code: '21212',
  display: 'Amalgam, 2 surface',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_RadiographBitewing: Coding = {
  code: '2141',
  display: 'Radiograph, bitewing',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_RadiographPanoramic: Coding = {
  code: '2601',
  display: 'Radiograph, panoramic',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_CrownPFM: Coding = {
  code: '27211',
  display: 'Crown, PFM',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_MarylandBridge: Coding = {
  code: '67211',
  display: 'Maryland Bridge',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_LabCommercial: Coding = {
  code: '99111',
  display: 'Lab, commercial',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_LabInOffice: Coding = {
  code: '99333',
  display: 'Lab, in office',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
const service_uscls_Expense: Coding = {
  code: '99555',
  display: 'Expense',
  system: 'http://terminology.hl7.org/CodeSystem/ex-USCLS',
};
/**
 * This value set includes a smattering of USCLS codes.
 */
export const ServiceUscls = {
  /**
   * Exam, comp, primary
   */
  ExamCompPrimary: service_uscls_ExamCompPrimary,
  /**
   * Exam, comp, mixed
   */
  ExamCompMixed: service_uscls_ExamCompMixed,
  /**
   * Exam, comp, permanent
   */
  ExamCompPermanent: service_uscls_ExamCompPermanent,
  /**
   * Polishing, 1 unit
   */
  Polishing1Unit: service_uscls_Polishing1Unit,
  /**
   * Polishing, 2 unit
   */
  Polishing2Unit: service_uscls_Polishing2Unit,
  /**
   * Polishing, 3 unit
   */
  Polishing3Unit: service_uscls_Polishing3Unit,
  /**
   * Polishing, 4 unit
   */
  Polishing4Unit: service_uscls_Polishing4Unit,
  /**
   * Exam, recall
   */
  ExamRecall: service_uscls_ExamRecall,
  /**
   * Exam, emergency
   */
  ExamEmergency: service_uscls_ExamEmergency,
  /**
   * Radiograph, series (12)
   */
  RadiographSeries12: service_uscls_RadiographSeries12,
  /**
   * Radiograph, series (16)
   */
  RadiographSeries16: service_uscls_RadiographSeries16,
  /**
   * Amalgam, 1 surface
   */
  Amalgam1Surface: service_uscls_Amalgam1Surface,
  /**
   * Amalgam, 2 surface
   */
  Amalgam2Surface: service_uscls_Amalgam2Surface,
  /**
   * Radiograph, bitewing
   */
  RadiographBitewing: service_uscls_RadiographBitewing,
  /**
   * Radiograph, panoramic
   */
  RadiographPanoramic: service_uscls_RadiographPanoramic,
  /**
   * Crown, PFM
   */
  CrownPFM: service_uscls_CrownPFM,
  /**
   * Maryland Bridge
   */
  MarylandBridge: service_uscls_MarylandBridge,
  /**
   * Lab, commercial
   */
  LabCommercial: service_uscls_LabCommercial,
  /**
   * Lab, in office
   */
  LabInOffice: service_uscls_LabInOffice,
  /**
   * Expense
   */
  Expense: service_uscls_Expense,
};
/**
 * The value set includes the v3 RoleCode SIB (sibling) and all of its specializations.  It covers the relationships needed to establish genetic pedigree relationships between family members.
 */
export const SiblingRelationshipCodes = {
  /**
   * The player of the role is a male sharing one or both parents in common with the scoping entity.
   */
  Brother: v3_RoleCode_Brother,
  /**
   * The scoper and player were carried in the same womb and share common biological parents but are the product of distinct egg/sperm pairs.
   */
  FraternalTwin: v3_RoleCode_FraternalTwin,
  /**
   * The scoper was carried in the same womb as the male player and shares common biological parents but is the product of a distinct egg/sperm pair.
   */
  FraternalTwinBrother: v3_RoleCode_FraternalTwinBrother,
  /**
   * The scoper was carried in the same womb as the female player and shares common biological parents but is the product of a distinct egg/sperm pair.
   */
  FraternalTwinSister: v3_RoleCode_FraternalTwinSister,
  /**
   * The player of the role is a male related to the scoping entity by sharing only one biological parent.
   */
  HalfBrother: v3_RoleCode_HalfBrother,
  /**
   * The player of the role is related to the scoping entity by sharing only one biological parent.
   */
  HalfSibling: v3_RoleCode_HalfSibling,
  /**
   * The player of the role is a female related to the scoping entity by sharing only one biological parent.
   */
  HalfSister: v3_RoleCode_HalfSister,
  /**
   * The scoper and player are offspring of the same egg-sperm pair.
   */
  IdenticalTwin: v3_RoleCode_IdenticalTwin,
  /**
   * The male scoper is an offspring of the same egg-sperm pair as the male player.
   */
  IdenticalTwinBrother: v3_RoleCode_IdenticalTwinBrother,
  /**
   * The female scoper is an offspring of the same egg-sperm pair as the female player.
   */
  IdenticalTwinSister: v3_RoleCode_IdenticalTwinSister,
  /**
   * The player of the role is a male having the same biological parents as the scoping entity.
   */
  NaturalBrother: v3_RoleCode_NaturalBrother,
  /**
   * The player of the role has both biological parents in common with the scoping entity.
   */
  NaturalSibling: v3_RoleCode_NaturalSibling,
  /**
   * The player of the role is a female having the same biological parents as the scoping entity.
   */
  NaturalSister: v3_RoleCode_NaturalSister,
  /**
   * The player of the role shares one or both parents in common with the scoping entity.
   */
  Sibling: v3_RoleCode_Sibling,
  /**
   * The player of the role is a female sharing one or both parents in common with the scoping entity.
   */
  Sister: v3_RoleCode_Sister,
  /**
   * The player of the role is a son of the scoping person's stepparent.
   */
  Stepbrother: v3_RoleCode_Stepbrother,
  /**
   * The player of the role is a child of the scoping person's stepparent.
   */
  StepSibling: v3_RoleCode_StepSibling,
  /**
   * The player of the role is a daughter of the scoping person's stepparent.
   */
  Stepsister: v3_RoleCode_Stepsister,
  /**
   * The scoper and player were carried in the same womb and shared common biological parents.
   */
  Twin: v3_RoleCode_Twin,
  /**
   * The scoper was carried in the same womb as the male player and shares common biological parents.
   */
  TwinBrother: v3_RoleCode_TwinBrother,
  /**
   * The scoper was carried in the same womb as the female player and shares common biological parents.
   */
  TwinSister: v3_RoleCode_TwinSister,
};
const signature_type_AuthorQuoteSSignature: Coding = {
  code: '1.2.840.10065.1.12.1.1',
  display: "Author's Signature",
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_IdentityWitnessSignature: Coding = {
  code: '1.2.840.10065.1.12.1.10',
  display: 'Identity Witness Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_ConsentWitnessSignature: Coding = {
  code: '1.2.840.10065.1.12.1.11',
  display: 'Consent Witness Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_InterpreterSignature: Coding = {
  code: '1.2.840.10065.1.12.1.12',
  display: 'Interpreter Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_ReviewSignature: Coding = {
  code: '1.2.840.10065.1.12.1.13',
  display: 'Review Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_SourceSignature: Coding = {
  code: '1.2.840.10065.1.12.1.14',
  display: 'Source Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_AddendumSignature: Coding = {
  code: '1.2.840.10065.1.12.1.15',
  display: 'Addendum Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_ModificationSignature: Coding = {
  code: '1.2.840.10065.1.12.1.16',
  display: 'Modification Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_AdministrativeErrorEditSignature: Coding = {
  code: '1.2.840.10065.1.12.1.17',
  display: 'Administrative (Error/Edit) Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_TimestampSignature: Coding = {
  code: '1.2.840.10065.1.12.1.18',
  display: 'Timestamp Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_CoauthorQuoteSSignature: Coding = {
  code: '1.2.840.10065.1.12.1.2',
  display: "Coauthor's Signature",
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_CoParticipantQuoteSSignature: Coding = {
  code: '1.2.840.10065.1.12.1.3',
  display: "Co-participant's Signature",
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_TranscriptionistRecorderSignature: Coding = {
  code: '1.2.840.10065.1.12.1.4',
  display: 'Transcriptionist/Recorder Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_VerificationSignature: Coding = {
  code: '1.2.840.10065.1.12.1.5',
  display: 'Verification Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_ValidationSignature: Coding = {
  code: '1.2.840.10065.1.12.1.6',
  display: 'Validation Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_ConsentSignature: Coding = {
  code: '1.2.840.10065.1.12.1.7',
  display: 'Consent Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_SignatureWitnessSignature: Coding = {
  code: '1.2.840.10065.1.12.1.8',
  display: 'Signature Witness Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
const signature_type_EventWitnessSignature: Coding = {
  code: '1.2.840.10065.1.12.1.9',
  display: 'Event Witness Signature',
  system: 'urn:iso-astm:E1762-95:2013',
};
/**
 * The Digital Signature Purposes, an indication of the reason an entity signs a document. This is included in the signed information and can be used when determining accountability for various actions concerning the document. Examples include: author, transcriptionist/recorder, and witness.
 */
export const SignatureType = {
  /**
   * the signature of the primary or sole author of a health information document. There can be only one primary author of a health information document.
   */
  AuthorQuoteSSignature: signature_type_AuthorQuoteSSignature,
  /**
   * the signature of an individual who has witnessed another individual who is known to them signing a document. (Example the identity witness is a notary public.)
   */
  IdentityWitnessSignature: signature_type_IdentityWitnessSignature,
  /**
   * the signature of an individual who has witnessed the health care provider counselling a patient.
   */
  ConsentWitnessSignature: signature_type_ConsentWitnessSignature,
  /**
   * the signature of an individual who has translated health care information during an event or the obtaining of consent to a treatment.
   */
  InterpreterSignature: signature_type_InterpreterSignature,
  /**
   * the signature of a person, device, or algorithm that has reviewed or filtered data for inclusion into the patient record. ( Examples: (1) a medical records clerk who scans a document for inclusion in the medical record, enters header information, or catalogues and classifies the data, or a combination thereof; (2) a gateway that receives data from another computer system and interprets that data or changes its format, or both, before entering it into the patient record.)
   */
  ReviewSignature: signature_type_ReviewSignature,
  /**
   * the signature of an automated data source. (Examples: (1) the signature for an image that is generated by a device for inclusion in the patient record; (2) the signature for an ECG derived by an ECG system for inclusion in the patient record; (3) the data from a biomedical monitoring device or system that is for inclusion in the patient record.)
   */
  SourceSignature: signature_type_SourceSignature,
  /**
   * the signature on a new amended document of an individual who has corrected, edited, or amended an original health information document. An addendum signature can either be a signature type or a signature sub-type (see 8.1). Any document with an addendum signature shall have a companion document that is the original document with its original, unaltered content, and original signatures. The original document shall be referenced via an attribute in the new document, which contains, for example, the digest of the old document. Whether the original, unaltered, document is always displayed with the addended document is a local matter, but the original, unaltered, document must remain as part of the patient record and be retrievable on demand.
   */
  AddendumSignature: signature_type_AddendumSignature,
  /**
   * the signature on an original document of an individual who has generated a new amended document. This (original) document shall reference the new document via an additional signature purpose. This is the inverse of an addendum signature and provides a pointer from the original to the amended document.
   */
  ModificationSignature: signature_type_ModificationSignature,
  /**
   * the signature of an individual who is certifying that the document is invalidated by an error(s), or is placed in the wrong chart. An administrative (error/edit) signature must include an addendum to the document and therefore shall have an addendum signature sub-type (see 8.1). This signature is reserved for the highest health information system administrative classification, since it is a statement that the entire document is invalidated by the error and that the document should no longer be used for patient care, although for legal reasons the document must remain part of the permanent patient record.
   */
  AdministrativeErrorEditSignature: signature_type_AdministrativeErrorEditSignature,
  /**
   * the signature by an entity or device trusted to provide accurate timestamps. This timestamp might be provided, for example, in the signature time attribute.
   */
  TimestampSignature: signature_type_TimestampSignature,
  /**
   * the signature of a health information document coauthor. There can be multiple coauthors of a health information document.
   */
  CoauthorQuoteSSignature: signature_type_CoauthorQuoteSSignature,
  /**
   * the signature of an individual who is a participant in the health information document but is not an author or coauthor. (Example a surgeon who is required by institutional, regulatory, or legal rules to sign an operative report, but who was not involved in the authorship of that report.)
   */
  CoParticipantQuoteSSignature: signature_type_CoParticipantQuoteSSignature,
  /**
   * the signature of an individual who has transcribed a dictated document or recorded written text into a digital machine readable format.
   */
  TranscriptionistRecorderSignature: signature_type_TranscriptionistRecorderSignature,
  /**
   * a signature verifying the information contained in a document. (Example a physician is required to countersign a verbal order that has previously been recorded in the medical record by a registered nurse who has carried out the verbal order.)
   */
  VerificationSignature: signature_type_VerificationSignature,
  /**
   * a signature validating a health information document for inclusion in the patient record. (Example a medical student or resident is credentialed to perform history or physical examinations and to write progress notes. The attending physician signs the history and physical examination to validate the entry for inclusion in the patient's medical record.)
   */
  ValidationSignature: signature_type_ValidationSignature,
  /**
   * the signature of an individual consenting to what is described in a health information document.
   */
  ConsentSignature: signature_type_ConsentSignature,
  /**
   * the signature of a witness to any other signature.
   */
  SignatureWitnessSignature: signature_type_SignatureWitnessSignature,
  /**
   * the signature of a witness to an event. (Example the witness has observed a procedure and is attesting to this fact.)
   */
  EventWitnessSignature: signature_type_EventWitnessSignature,
};
const slotstatus_Busy: Coding = {
  code: 'busy',
  display: 'Busy',
  system: 'http://hl7.org/fhir/slotstatus',
};
const slotstatus_BusyTentative: Coding = {
  code: 'busy-tentative',
  display: 'Busy (Tentative)',
  system: 'http://hl7.org/fhir/slotstatus',
};
const slotstatus_BusyUnavailable: Coding = {
  code: 'busy-unavailable',
  display: 'Busy (Unavailable)',
  system: 'http://hl7.org/fhir/slotstatus',
};
const slotstatus_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in error',
  system: 'http://hl7.org/fhir/slotstatus',
};
const slotstatus_Free: Coding = {
  code: 'free',
  display: 'Free',
  system: 'http://hl7.org/fhir/slotstatus',
};
/**
 * The free/busy status of the slot.
 */
export const Slotstatus = {
  /**
   * Indicates that the time interval is busy because one  or more events have been scheduled for that interval.
   */
  Busy: slotstatus_Busy,
  /**
   * Indicates that the time interval is busy because one or more events have been tentatively scheduled for that interval.
   */
  BusyTentative: slotstatus_BusyTentative,
  /**
   * Indicates that the time interval is busy and that the interval cannot be scheduled.
   */
  BusyUnavailable: slotstatus_BusyUnavailable,
  /**
   * This instance should not have been part of this patient's medical record.
   */
  EnteredInError: slotstatus_EnteredInError,
  /**
   * Indicates that the time interval is free for scheduling.
   */
  Free: slotstatus_Free,
};
const smart_capabilities_ClientConfidentialSymmetric: Coding = {
  code: 'client-confidential-symmetric',
  display: 'Confidential Client Profile',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
const smart_capabilities_ClientPublic: Coding = {
  code: 'client-public',
  display: 'Public Client Profile',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
const smart_capabilities_ContextEhrEncounter: Coding = {
  code: 'context-ehr-encounter',
  display: 'Allows "Encounter Level Launch Context (EHR)"',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
const smart_capabilities_ContextEhrPatient: Coding = {
  code: 'context-ehr-patient',
  display: 'Allows "Patient Level Launch Context (EHR)"',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
const smart_capabilities_ContextPassthroughBanner: Coding = {
  code: 'context-passthrough-banner',
  display: 'Allows "Need Patient Banner"',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
const smart_capabilities_ContextPassthroughStyle: Coding = {
  code: 'context-passthrough-style',
  display: 'Allows "Smart Style Style"',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
const smart_capabilities_ContextStandaloneEncounter: Coding = {
  code: 'context-standalone-encounter',
  display: 'Allows "Encounter Level Launch Context (STANDALONE)"',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
const smart_capabilities_ContextStandalonePatient: Coding = {
  code: 'context-standalone-patient',
  display: 'Allows "Patient Level Launch Context (STANDALONE)"',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
const smart_capabilities_LaunchEhr: Coding = {
  code: 'launch-ehr',
  display: 'EHR Launch Mode',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
const smart_capabilities_LaunchStandalone: Coding = {
  code: 'launch-standalone',
  display: 'Standalone Launch Mode',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
const smart_capabilities_PermissionOffline: Coding = {
  code: 'permission-offline',
  display: 'Supports Refresh Token',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
const smart_capabilities_PermissionPatient: Coding = {
  code: 'permission-patient',
  display: 'Supports Patient Level Scopes',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
const smart_capabilities_PermissionUser: Coding = {
  code: 'permission-user',
  display: 'Supports User Level Scopes',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
const smart_capabilities_SsoOpenidConnect: Coding = {
  code: 'sso-openid-connect',
  display: 'Supports OpenID Connect',
  system: 'http://terminology.hl7.org/CodeSystem/smart-capabilities',
};
/**
 * Codes that define what the server is capable of.
 */
export const SmartCapabilities = {
  /**
   * support for SMART’s confidential client profile (symmetric client secret authentication).
   */
  ClientConfidentialSymmetric: smart_capabilities_ClientConfidentialSymmetric,
  /**
   * support for SMART’s public client profile (no client authentication).
   */
  ClientPublic: smart_capabilities_ClientPublic,
  /**
   * support for encounter-level launch context (requested by launch/encounter scope, conveyed via encounter token parameter).
   */
  ContextEhrEncounter: smart_capabilities_ContextEhrEncounter,
  /**
   * support for patient-level launch context (requested by launch/patient scope, conveyed via patient token parameter).
   */
  ContextEhrPatient: smart_capabilities_ContextEhrPatient,
  /**
   * support for “need patient banner” launch context (conveyed via need_patient_banner token parameter).
   */
  ContextPassthroughBanner: smart_capabilities_ContextPassthroughBanner,
  /**
   * support for “SMART style URL” launch context (conveyed via smart_style_url token parameter).
   */
  ContextPassthroughStyle: smart_capabilities_ContextPassthroughStyle,
  /**
   * support for encounter-level launch context (requested by launch/encounter scope, conveyed via encounter token parameter).
   */
  ContextStandaloneEncounter: smart_capabilities_ContextStandaloneEncounter,
  /**
   * support for patient-level launch context (requested by launch/patient scope, conveyed via patient token parameter).
   */
  ContextStandalonePatient: smart_capabilities_ContextStandalonePatient,
  /**
   * support for SMART’s EHR Launch mode.
   */
  LaunchEhr: smart_capabilities_LaunchEhr,
  /**
   * support for SMART’s Standalone Launch mode.
   */
  LaunchStandalone: smart_capabilities_LaunchStandalone,
  /**
   * support for refresh tokens (requested by offline_access scope).
   */
  PermissionOffline: smart_capabilities_PermissionOffline,
  /**
   * support for patient-level scopes (e.g. patient/Observation.read).
   */
  PermissionPatient: smart_capabilities_PermissionPatient,
  /**
   * support for user-level scopes (e.g. user/Appointment.read).
   */
  PermissionUser: smart_capabilities_PermissionUser,
  /**
   * support for SMART’s OpenID Connect profile.
   */
  SsoOpenidConnect: smart_capabilities_SsoOpenidConnect,
};
const sort_direction_Ascending: Coding = {
  code: 'ascending',
  display: 'Ascending',
  system: 'http://hl7.org/fhir/sort-direction',
};
const sort_direction_Descending: Coding = {
  code: 'descending',
  display: 'Descending',
  system: 'http://hl7.org/fhir/sort-direction',
};
/**
 * The possible sort directions, ascending or descending.
 */
export const SortDirection = {
  /**
   * Sort by the value ascending, so that lower values appear first.
   */
  Ascending: sort_direction_Ascending,
  /**
   * Sort by the value descending, so that lower values appear last.
   */
  Descending: sort_direction_Descending,
};
const spdx_license_BSDZeroClauseLicense: Coding = {
  code: '0BSD',
  display: 'BSD Zero Clause License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AttributionAssuranceLicense: Coding = {
  code: 'AAL',
  display: 'Attribution Assurance License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AbstylesLicense: Coding = {
  code: 'Abstyles',
  display: 'Abstyles License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AdobeSystemsIncorporatedSourceCodeLicenseAgreement: Coding = {
  code: 'Adobe-2006',
  display: 'Adobe Systems Incorporated Source Code License Agreement',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AdobeGlyphListLicense: Coding = {
  code: 'Adobe-Glyph',
  display: 'Adobe Glyph List License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AmazonDigitalServicesLicense: Coding = {
  code: 'ADSL',
  display: 'Amazon Digital Services License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AcademicFreeLicenseV11: Coding = {
  code: 'AFL-1.1',
  display: 'Academic Free License v1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AcademicFreeLicenseV12: Coding = {
  code: 'AFL-1.2',
  display: 'Academic Free License v1.2',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AcademicFreeLicenseV20: Coding = {
  code: 'AFL-2.0',
  display: 'Academic Free License v2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AcademicFreeLicenseV21: Coding = {
  code: 'AFL-2.1',
  display: 'Academic Free License v2.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AcademicFreeLicenseV30: Coding = {
  code: 'AFL-3.0',
  display: 'Academic Free License v3.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AfmparseLicense: Coding = {
  code: 'Afmparse',
  display: 'Afmparse License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AfferoGeneralPublicLicenseV10Only: Coding = {
  code: 'AGPL-1.0-only',
  display: 'Affero General Public License v1.0 only',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AfferoGeneralPublicLicenseV10OrLater: Coding = {
  code: 'AGPL-1.0-or-later',
  display: 'Affero General Public License v1.0 or later',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUAfferoGeneralPublicLicenseV30Only: Coding = {
  code: 'AGPL-3.0-only',
  display: 'GNU Affero General Public License v3.0 only',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUAfferoGeneralPublicLicenseV30OrLater: Coding = {
  code: 'AGPL-3.0-or-later',
  display: 'GNU Affero General Public License v3.0 or later',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AladdinFreePublicLicense: Coding = {
  code: 'Aladdin',
  display: 'Aladdin Free Public License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AMDQuoteSPlpaMapCLicense: Coding = {
  code: 'AMDPLPA',
  display: "AMD's plpa_map.c License",
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AppleMITLicense: Coding = {
  code: 'AML',
  display: 'Apple MIT License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AcademyOfMotionPictureArtsAndSciencesBSD: Coding = {
  code: 'AMPAS',
  display: 'Academy of Motion Picture Arts and Sciences BSD',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ANTLRSoftwareRightsNotice: Coding = {
  code: 'ANTLR-PD',
  display: 'ANTLR Software Rights Notice',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ApacheLicense10: Coding = {
  code: 'Apache-1.0',
  display: 'Apache License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ApacheLicense11: Coding = {
  code: 'Apache-1.1',
  display: 'Apache License 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ApacheLicense20: Coding = {
  code: 'Apache-2.0',
  display: 'Apache License 2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AdobePostscriptAFMLicense: Coding = {
  code: 'APAFML',
  display: 'Adobe Postscript AFM License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_AdaptivePublicLicense10: Coding = {
  code: 'APL-1.0',
  display: 'Adaptive Public License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ApplePublicSourceLicense10: Coding = {
  code: 'APSL-1.0',
  display: 'Apple Public Source License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ApplePublicSourceLicense11: Coding = {
  code: 'APSL-1.1',
  display: 'Apple Public Source License 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ApplePublicSourceLicense12: Coding = {
  code: 'APSL-1.2',
  display: 'Apple Public Source License 1.2',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ApplePublicSourceLicense20: Coding = {
  code: 'APSL-2.0',
  display: 'Apple Public Source License 2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ArtisticLicense10: Coding = {
  code: 'Artistic-1.0',
  display: 'Artistic License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ArtisticLicense10WClause8: Coding = {
  code: 'Artistic-1.0-cl8',
  display: 'Artistic License 1.0 w/clause 8',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ArtisticLicense10Perl: Coding = {
  code: 'Artistic-1.0-Perl',
  display: 'Artistic License 1.0 (Perl)',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ArtisticLicense20: Coding = {
  code: 'Artistic-2.0',
  display: 'Artistic License 2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BahyphLicense: Coding = {
  code: 'Bahyph',
  display: 'Bahyph License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BarrLicense: Coding = {
  code: 'Barr',
  display: 'Barr License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BeerwareLicense: Coding = {
  code: 'Beerware',
  display: 'Beerware License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BitTorrentOpenSourceLicenseV10: Coding = {
  code: 'BitTorrent-1.0',
  display: 'BitTorrent Open Source License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BitTorrentOpenSourceLicenseV11: Coding = {
  code: 'BitTorrent-1.1',
  display: 'BitTorrent Open Source License v1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BorceuxLicense: Coding = {
  code: 'Borceux',
  display: 'Borceux license',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSD1ClauseLicense: Coding = {
  code: 'BSD-1-Clause',
  display: 'BSD 1-Clause License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSD2ClauseQuotationSimplifiedQuotationLicense: Coding = {
  code: 'BSD-2-Clause',
  display: 'BSD 2-Clause "Simplified" License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSD2ClauseFreeBSDLicense: Coding = {
  code: 'BSD-2-Clause-FreeBSD',
  display: 'BSD 2-Clause FreeBSD License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSD2ClauseNetBSDLicense: Coding = {
  code: 'BSD-2-Clause-NetBSD',
  display: 'BSD 2-Clause NetBSD License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSD2ClausePlusPatentLicense: Coding = {
  code: 'BSD-2-Clause-Patent',
  display: 'BSD-2-Clause Plus Patent License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSD3ClauseQuotationNewQuotationOrQuotationRevisedQuotationLicense: Coding = {
  code: 'BSD-3-Clause',
  display: 'BSD 3-Clause "New" or "Revised" License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSDWithAttribution: Coding = {
  code: 'BSD-3-Clause-Attribution',
  display: 'BSD with attribution',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSD3ClauseClearLicense: Coding = {
  code: 'BSD-3-Clause-Clear',
  display: 'BSD 3-Clause Clear License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LawrenceBerkeleyNationalLabsBSDVariantLicense: Coding = {
  code: 'BSD-3-Clause-LBNL',
  display: 'Lawrence Berkeley National Labs BSD variant license',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSD3ClauseNoNuclearLicense: Coding = {
  code: 'BSD-3-Clause-No-Nuclear-License',
  display: 'BSD 3-Clause No Nuclear License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSD3ClauseNoNuclearLicense2014: Coding = {
  code: 'BSD-3-Clause-No-Nuclear-License-2014',
  display: 'BSD 3-Clause No Nuclear License 2014',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSD3ClauseNoNuclearWarranty: Coding = {
  code: 'BSD-3-Clause-No-Nuclear-Warranty',
  display: 'BSD 3-Clause No Nuclear Warranty',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSD4ClauseQuotationOriginalQuotationOrQuotationOldQuotationLicense: Coding = {
  code: 'BSD-4-Clause',
  display: 'BSD 4-Clause "Original" or "Old" License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSD4ClauseUniversityOfCaliforniaSpecific: Coding = {
  code: 'BSD-4-Clause-UC',
  display: 'BSD-4-Clause (University of California-Specific)',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSDProtectionLicense: Coding = {
  code: 'BSD-Protection',
  display: 'BSD Protection License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BSDSourceCodeAttribution: Coding = {
  code: 'BSD-Source-Code',
  display: 'BSD Source Code Attribution',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_BoostSoftwareLicense10: Coding = {
  code: 'BSL-1.0',
  display: 'Boost Software License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_Bzip2AndLibbzip2LicenseV105: Coding = {
  code: 'bzip2-1.0.5',
  display: 'bzip2 and libbzip2 License v1.0.5',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_Bzip2AndLibbzip2LicenseV106: Coding = {
  code: 'bzip2-1.0.6',
  display: 'bzip2 and libbzip2 License v1.0.6',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CalderaLicense: Coding = {
  code: 'Caldera',
  display: 'Caldera License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ComputerAssociatesTrustedOpenSourceLicense11: Coding = {
  code: 'CATOSL-1.1',
  display: 'Computer Associates Trusted Open Source License 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsZeroV10Universal: Coding = {
  code: 'CC0-1.0',
  display: 'Creative Commons Zero v1.0 Universal',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttribution10Generic: Coding = {
  code: 'CC-BY-1.0',
  display: 'Creative Commons Attribution 1.0 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttribution20Generic: Coding = {
  code: 'CC-BY-2.0',
  display: 'Creative Commons Attribution 2.0 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttribution25Generic: Coding = {
  code: 'CC-BY-2.5',
  display: 'Creative Commons Attribution 2.5 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttribution30Unported: Coding = {
  code: 'CC-BY-3.0',
  display: 'Creative Commons Attribution 3.0 Unported',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttribution40International: Coding = {
  code: 'CC-BY-4.0',
  display: 'Creative Commons Attribution 4.0 International',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercial10Generic: Coding = {
  code: 'CC-BY-NC-1.0',
  display: 'Creative Commons Attribution Non Commercial 1.0 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercial20Generic: Coding = {
  code: 'CC-BY-NC-2.0',
  display: 'Creative Commons Attribution Non Commercial 2.0 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercial25Generic: Coding = {
  code: 'CC-BY-NC-2.5',
  display: 'Creative Commons Attribution Non Commercial 2.5 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercial30Unported: Coding = {
  code: 'CC-BY-NC-3.0',
  display: 'Creative Commons Attribution Non Commercial 3.0 Unported',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercial40International: Coding = {
  code: 'CC-BY-NC-4.0',
  display: 'Creative Commons Attribution Non Commercial 4.0 International',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercialNoDerivatives10Generic: Coding = {
  code: 'CC-BY-NC-ND-1.0',
  display: 'Creative Commons Attribution Non Commercial No Derivatives 1.0 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercialNoDerivatives20Generic: Coding = {
  code: 'CC-BY-NC-ND-2.0',
  display: 'Creative Commons Attribution Non Commercial No Derivatives 2.0 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercialNoDerivatives25Generic: Coding = {
  code: 'CC-BY-NC-ND-2.5',
  display: 'Creative Commons Attribution Non Commercial No Derivatives 2.5 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercialNoDerivatives30Unported: Coding = {
  code: 'CC-BY-NC-ND-3.0',
  display: 'Creative Commons Attribution Non Commercial No Derivatives 3.0 Unported',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercialNoDerivatives40International: Coding = {
  code: 'CC-BY-NC-ND-4.0',
  display: 'Creative Commons Attribution Non Commercial No Derivatives 4.0 International',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercialShareAlike10Generic: Coding = {
  code: 'CC-BY-NC-SA-1.0',
  display: 'Creative Commons Attribution Non Commercial Share Alike 1.0 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercialShareAlike20Generic: Coding = {
  code: 'CC-BY-NC-SA-2.0',
  display: 'Creative Commons Attribution Non Commercial Share Alike 2.0 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercialShareAlike25Generic: Coding = {
  code: 'CC-BY-NC-SA-2.5',
  display: 'Creative Commons Attribution Non Commercial Share Alike 2.5 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercialShareAlike30Unported: Coding = {
  code: 'CC-BY-NC-SA-3.0',
  display: 'Creative Commons Attribution Non Commercial Share Alike 3.0 Unported',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNonCommercialShareAlike40International: Coding = {
  code: 'CC-BY-NC-SA-4.0',
  display: 'Creative Commons Attribution Non Commercial Share Alike 4.0 International',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNoDerivatives10Generic: Coding = {
  code: 'CC-BY-ND-1.0',
  display: 'Creative Commons Attribution No Derivatives 1.0 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNoDerivatives20Generic: Coding = {
  code: 'CC-BY-ND-2.0',
  display: 'Creative Commons Attribution No Derivatives 2.0 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNoDerivatives25Generic: Coding = {
  code: 'CC-BY-ND-2.5',
  display: 'Creative Commons Attribution No Derivatives 2.5 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNoDerivatives30Unported: Coding = {
  code: 'CC-BY-ND-3.0',
  display: 'Creative Commons Attribution No Derivatives 3.0 Unported',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionNoDerivatives40International: Coding = {
  code: 'CC-BY-ND-4.0',
  display: 'Creative Commons Attribution No Derivatives 4.0 International',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionShareAlike10Generic: Coding = {
  code: 'CC-BY-SA-1.0',
  display: 'Creative Commons Attribution Share Alike 1.0 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionShareAlike20Generic: Coding = {
  code: 'CC-BY-SA-2.0',
  display: 'Creative Commons Attribution Share Alike 2.0 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionShareAlike25Generic: Coding = {
  code: 'CC-BY-SA-2.5',
  display: 'Creative Commons Attribution Share Alike 2.5 Generic',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionShareAlike30Unported: Coding = {
  code: 'CC-BY-SA-3.0',
  display: 'Creative Commons Attribution Share Alike 3.0 Unported',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CreativeCommonsAttributionShareAlike40International: Coding = {
  code: 'CC-BY-SA-4.0',
  display: 'Creative Commons Attribution Share Alike 4.0 International',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CommonDevelopmentAndDistributionLicense10: Coding = {
  code: 'CDDL-1.0',
  display: 'Common Development and Distribution License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CommonDevelopmentAndDistributionLicense11: Coding = {
  code: 'CDDL-1.1',
  display: 'Common Development and Distribution License 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CommunityDataLicenseAgreementPermissive10: Coding = {
  code: 'CDLA-Permissive-1.0',
  display: 'Community Data License Agreement Permissive 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CommunityDataLicenseAgreementSharing10: Coding = {
  code: 'CDLA-Sharing-1.0',
  display: 'Community Data License Agreement Sharing 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CeCILLFreeSoftwareLicenseAgreementV10: Coding = {
  code: 'CECILL-1.0',
  display: 'CeCILL Free Software License Agreement v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CeCILLFreeSoftwareLicenseAgreementV11: Coding = {
  code: 'CECILL-1.1',
  display: 'CeCILL Free Software License Agreement v1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CeCILLFreeSoftwareLicenseAgreementV20: Coding = {
  code: 'CECILL-2.0',
  display: 'CeCILL Free Software License Agreement v2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CeCILLFreeSoftwareLicenseAgreementV21: Coding = {
  code: 'CECILL-2.1',
  display: 'CeCILL Free Software License Agreement v2.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CeCILLBFreeSoftwareLicenseAgreement: Coding = {
  code: 'CECILL-B',
  display: 'CeCILL-B Free Software License Agreement',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CeCILLCFreeSoftwareLicenseAgreement: Coding = {
  code: 'CECILL-C',
  display: 'CeCILL-C Free Software License Agreement',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ClarifiedArtisticLicense: Coding = {
  code: 'ClArtistic',
  display: 'Clarified Artistic License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CNRIJythonLicense: Coding = {
  code: 'CNRI-Jython',
  display: 'CNRI Jython License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CNRIPythonLicense: Coding = {
  code: 'CNRI-Python',
  display: 'CNRI Python License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CNRIPythonOpenSourceGPLCompatibleLicenseAgreement: Coding = {
  code: 'CNRI-Python-GPL-Compatible',
  display: 'CNRI Python Open Source GPL Compatible License Agreement',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CondorPublicLicenseV11: Coding = {
  code: 'Condor-1.1',
  display: 'Condor Public License v1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CommonPublicAttributionLicense10: Coding = {
  code: 'CPAL-1.0',
  display: 'Common Public Attribution License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CommonPublicLicense10: Coding = {
  code: 'CPL-1.0',
  display: 'Common Public License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CodeProjectOpenLicense102: Coding = {
  code: 'CPOL-1.02',
  display: 'Code Project Open License 1.02',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CrosswordLicense: Coding = {
  code: 'Crossword',
  display: 'Crossword License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CrystalStackerLicense: Coding = {
  code: 'CrystalStacker',
  display: 'CrystalStacker License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CUAOfficePublicLicenseV10: Coding = {
  code: 'CUA-OPL-1.0',
  display: 'CUA Office Public License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CubeLicense: Coding = {
  code: 'Cube',
  display: 'Cube License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CurlLicense: Coding = {
  code: 'curl',
  display: 'curl License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_DeutscheFreieSoftwareLizenz: Coding = {
  code: 'D-FSL-1.0',
  display: 'Deutsche Freie Software Lizenz',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_DiffmarkLicense: Coding = {
  code: 'diffmark',
  display: 'diffmark license',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_DOCLicense: Coding = {
  code: 'DOC',
  display: 'DOC License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_DotseqnLicense: Coding = {
  code: 'Dotseqn',
  display: 'Dotseqn License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_DSDPLicense: Coding = {
  code: 'DSDP',
  display: 'DSDP License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_DvipdfmLicense: Coding = {
  code: 'dvipdfm',
  display: 'dvipdfm License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EducationalCommunityLicenseV10: Coding = {
  code: 'ECL-1.0',
  display: 'Educational Community License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EducationalCommunityLicenseV20: Coding = {
  code: 'ECL-2.0',
  display: 'Educational Community License v2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EiffelForumLicenseV10: Coding = {
  code: 'EFL-1.0',
  display: 'Eiffel Forum License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EiffelForumLicenseV20: Coding = {
  code: 'EFL-2.0',
  display: 'Eiffel Forum License v2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EGenixComPublicLicense110: Coding = {
  code: 'eGenix',
  display: 'eGenix.com Public License 1.1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EntessaPublicLicenseV10: Coding = {
  code: 'Entessa',
  display: 'Entessa Public License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EclipsePublicLicense10: Coding = {
  code: 'EPL-1.0',
  display: 'Eclipse Public License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EclipsePublicLicense20: Coding = {
  code: 'EPL-2.0',
  display: 'Eclipse Public License 2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ErlangPublicLicenseV11: Coding = {
  code: 'ErlPL-1.1',
  display: 'Erlang Public License v1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EUDataGridSoftwareLicense: Coding = {
  code: 'EUDatagrid',
  display: 'EU DataGrid Software License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EuropeanUnionPublicLicense10: Coding = {
  code: 'EUPL-1.0',
  display: 'European Union Public License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EuropeanUnionPublicLicense11: Coding = {
  code: 'EUPL-1.1',
  display: 'European Union Public License 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EuropeanUnionPublicLicense12: Coding = {
  code: 'EUPL-1.2',
  display: 'European Union Public License 1.2',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EurosymLicense: Coding = {
  code: 'Eurosym',
  display: 'Eurosym License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_FairLicense: Coding = {
  code: 'Fair',
  display: 'Fair License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_FrameworxOpenLicense10: Coding = {
  code: 'Frameworx-1.0',
  display: 'Frameworx Open License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_FreeImagePublicLicenseV10: Coding = {
  code: 'FreeImage',
  display: 'FreeImage Public License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_FSFAllPermissiveLicense: Coding = {
  code: 'FSFAP',
  display: 'FSF All Permissive License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_FSFUnlimitedLicense: Coding = {
  code: 'FSFUL',
  display: 'FSF Unlimited License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_FSFUnlimitedLicenseWithLicenseRetention: Coding = {
  code: 'FSFULLR',
  display: 'FSF Unlimited License (with License Retention)',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_FreetypeProjectLicense: Coding = {
  code: 'FTL',
  display: 'Freetype Project License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUFreeDocumentationLicenseV11Only: Coding = {
  code: 'GFDL-1.1-only',
  display: 'GNU Free Documentation License v1.1 only',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUFreeDocumentationLicenseV11OrLater: Coding = {
  code: 'GFDL-1.1-or-later',
  display: 'GNU Free Documentation License v1.1 or later',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUFreeDocumentationLicenseV12Only: Coding = {
  code: 'GFDL-1.2-only',
  display: 'GNU Free Documentation License v1.2 only',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUFreeDocumentationLicenseV12OrLater: Coding = {
  code: 'GFDL-1.2-or-later',
  display: 'GNU Free Documentation License v1.2 or later',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUFreeDocumentationLicenseV13Only: Coding = {
  code: 'GFDL-1.3-only',
  display: 'GNU Free Documentation License v1.3 only',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUFreeDocumentationLicenseV13OrLater: Coding = {
  code: 'GFDL-1.3-or-later',
  display: 'GNU Free Documentation License v1.3 or later',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GiftwareLicense: Coding = {
  code: 'Giftware',
  display: 'Giftware License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GL2PSLicense: Coding = {
  code: 'GL2PS',
  display: 'GL2PS License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_VAL3dfxGlideLicense: Coding = {
  code: 'Glide',
  display: '3dfx Glide License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GlulxeLicense: Coding = {
  code: 'Glulxe',
  display: 'Glulxe License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GnuplotLicense: Coding = {
  code: 'gnuplot',
  display: 'gnuplot License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUGeneralPublicLicenseV10Only: Coding = {
  code: 'GPL-1.0-only',
  display: 'GNU General Public License v1.0 only',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUGeneralPublicLicenseV10OrLater: Coding = {
  code: 'GPL-1.0-or-later',
  display: 'GNU General Public License v1.0 or later',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUGeneralPublicLicenseV20Only: Coding = {
  code: 'GPL-2.0-only',
  display: 'GNU General Public License v2.0 only',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUGeneralPublicLicenseV20OrLater: Coding = {
  code: 'GPL-2.0-or-later',
  display: 'GNU General Public License v2.0 or later',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUGeneralPublicLicenseV30Only: Coding = {
  code: 'GPL-3.0-only',
  display: 'GNU General Public License v3.0 only',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNUGeneralPublicLicenseV30OrLater: Coding = {
  code: 'GPL-3.0-or-later',
  display: 'GNU General Public License v3.0 or later',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GSOAPPublicLicenseV13b: Coding = {
  code: 'gSOAP-1.3b',
  display: 'gSOAP Public License v1.3b',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_HaskellLanguageReportLicense: Coding = {
  code: 'HaskellReport',
  display: 'Haskell Language Report License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_HistoricalPermissionNoticeAndDisclaimer: Coding = {
  code: 'HPND',
  display: 'Historical Permission Notice and Disclaimer',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_IBMPowerPCInitializationAndBootSoftware: Coding = {
  code: 'IBM-pibs',
  display: 'IBM PowerPC Initialization and Boot Software',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ICULicense: Coding = {
  code: 'ICU',
  display: 'ICU License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_IndependentJPEGGroupLicense: Coding = {
  code: 'IJG',
  display: 'Independent JPEG Group License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ImageMagickLicense: Coding = {
  code: 'ImageMagick',
  display: 'ImageMagick License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_IMatixStandardFunctionLibraryAgreement: Coding = {
  code: 'iMatix',
  display: 'iMatix Standard Function Library Agreement',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_Imlib2License: Coding = {
  code: 'Imlib2',
  display: 'Imlib2 License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_InfoZIPLicense: Coding = {
  code: 'Info-ZIP',
  display: 'Info-ZIP License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_IntelOpenSourceLicense: Coding = {
  code: 'Intel',
  display: 'Intel Open Source License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_IntelACPISoftwareLicenseAgreement: Coding = {
  code: 'Intel-ACPI',
  display: 'Intel ACPI Software License Agreement',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_InterbasePublicLicenseV10: Coding = {
  code: 'Interbase-1.0',
  display: 'Interbase Public License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_IPAFontLicense: Coding = {
  code: 'IPA',
  display: 'IPA Font License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_IBMPublicLicenseV10: Coding = {
  code: 'IPL-1.0',
  display: 'IBM Public License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ISCLicense: Coding = {
  code: 'ISC',
  display: 'ISC License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_JasPerLicense: Coding = {
  code: 'JasPer-2.0',
  display: 'JasPer License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_JSONLicense: Coding = {
  code: 'JSON',
  display: 'JSON License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LicenceArtLibre12: Coding = {
  code: 'LAL-1.2',
  display: 'Licence Art Libre 1.2',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LicenceArtLibre13: Coding = {
  code: 'LAL-1.3',
  display: 'Licence Art Libre 1.3',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_Latex2eLicense: Coding = {
  code: 'Latex2e',
  display: 'Latex2e License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LeptonicaLicense: Coding = {
  code: 'Leptonica',
  display: 'Leptonica License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNULibraryGeneralPublicLicenseV2Only: Coding = {
  code: 'LGPL-2.0-only',
  display: 'GNU Library General Public License v2 only',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNULibraryGeneralPublicLicenseV2OrLater: Coding = {
  code: 'LGPL-2.0-or-later',
  display: 'GNU Library General Public License v2 or later',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNULesserGeneralPublicLicenseV21Only: Coding = {
  code: 'LGPL-2.1-only',
  display: 'GNU Lesser General Public License v2.1 only',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNULesserGeneralPublicLicenseV21OrLater: Coding = {
  code: 'LGPL-2.1-or-later',
  display: 'GNU Lesser General Public License v2.1 or later',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNULesserGeneralPublicLicenseV30Only: Coding = {
  code: 'LGPL-3.0-only',
  display: 'GNU Lesser General Public License v3.0 only',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_GNULesserGeneralPublicLicenseV30OrLater: Coding = {
  code: 'LGPL-3.0-or-later',
  display: 'GNU Lesser General Public License v3.0 or later',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LesserGeneralPublicLicenseForLinguisticResources: Coding = {
  code: 'LGPLLR',
  display: 'Lesser General Public License For Linguistic Resources',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LibpngLicense: Coding = {
  code: 'Libpng',
  display: 'libpng License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LibtiffLicense: Coding = {
  code: 'libtiff',
  display: 'libtiff License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LicenceLibreDuQubecPermissiveVersion11: Coding = {
  code: 'LiLiQ-P-1.1',
  display: 'Licence Libre du Québec – Permissive version 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LicenceLibreDuQubecRciprocitVersion11: Coding = {
  code: 'LiLiQ-R-1.1',
  display: 'Licence Libre du Québec – Réciprocité version 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LicenceLibreDuQubecRciprocitForteVersion11: Coding = {
  code: 'LiLiQ-Rplus-1.1',
  display: 'Licence Libre du Québec – Réciprocité forte version 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LinuxKernelVariantOfOpenIBOrgLicense: Coding = {
  code: 'Linux-OpenIB',
  display: 'Linux Kernel Variant of OpenIB.org license',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LucentPublicLicenseVersion10: Coding = {
  code: 'LPL-1.0',
  display: 'Lucent Public License Version 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LucentPublicLicenseV102: Coding = {
  code: 'LPL-1.02',
  display: 'Lucent Public License v1.02',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LaTeXProjectPublicLicenseV10: Coding = {
  code: 'LPPL-1.0',
  display: 'LaTeX Project Public License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LaTeXProjectPublicLicenseV11: Coding = {
  code: 'LPPL-1.1',
  display: 'LaTeX Project Public License v1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LaTeXProjectPublicLicenseV12: Coding = {
  code: 'LPPL-1.2',
  display: 'LaTeX Project Public License v1.2',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LaTeXProjectPublicLicenseV13a: Coding = {
  code: 'LPPL-1.3a',
  display: 'LaTeX Project Public License v1.3a',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_LaTeXProjectPublicLicenseV13c: Coding = {
  code: 'LPPL-1.3c',
  display: 'LaTeX Project Public License v1.3c',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MakeIndexLicense: Coding = {
  code: 'MakeIndex',
  display: 'MakeIndex License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MirOSLicense: Coding = {
  code: 'MirOS',
  display: 'MirOS License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MITLicense: Coding = {
  code: 'MIT',
  display: 'MIT License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MITNoAttribution: Coding = {
  code: 'MIT-0',
  display: 'MIT No Attribution',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EnlightenmentLicenseE16: Coding = {
  code: 'MIT-advertising',
  display: 'Enlightenment License (e16)',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_CMULicense: Coding = {
  code: 'MIT-CMU',
  display: 'CMU License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_EnnaLicense: Coding = {
  code: 'MIT-enna',
  display: 'enna License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_FehLicense: Coding = {
  code: 'MIT-feh',
  display: 'feh License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MITPlusNoFalseAttribsLicense: Coding = {
  code: 'MITNFA',
  display: 'MIT +no-false-attribs license',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MotosotoLicense: Coding = {
  code: 'Motosoto',
  display: 'Motosoto License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_Mpich2License: Coding = {
  code: 'mpich2',
  display: 'mpich2 License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MozillaPublicLicense10: Coding = {
  code: 'MPL-1.0',
  display: 'Mozilla Public License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MozillaPublicLicense11: Coding = {
  code: 'MPL-1.1',
  display: 'Mozilla Public License 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MozillaPublicLicense20: Coding = {
  code: 'MPL-2.0',
  display: 'Mozilla Public License 2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MozillaPublicLicense20NoCopyleftException: Coding = {
  code: 'MPL-2.0-no-copyleft-exception',
  display: 'Mozilla Public License 2.0 (no copyleft exception)',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MicrosoftPublicLicense: Coding = {
  code: 'MS-PL',
  display: 'Microsoft Public License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MicrosoftReciprocalLicense: Coding = {
  code: 'MS-RL',
  display: 'Microsoft Reciprocal License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MatrixTemplateLibraryLicense: Coding = {
  code: 'MTLL',
  display: 'Matrix Template Library License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MulticsLicense: Coding = {
  code: 'Multics',
  display: 'Multics License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_MupLicense: Coding = {
  code: 'Mup',
  display: 'Mup License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NASAOpenSourceAgreement13: Coding = {
  code: 'NASA-1.3',
  display: 'NASA Open Source Agreement 1.3',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NaumenPublicLicense: Coding = {
  code: 'Naumen',
  display: 'Naumen Public License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NetBooleanPublicLicenseV1: Coding = {
  code: 'NBPL-1.0',
  display: 'Net Boolean Public License v1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_UniversityOfIllinoisNCSAOpenSourceLicense: Coding = {
  code: 'NCSA',
  display: 'University of Illinois/NCSA Open Source License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NetCDFLicense: Coding = {
  code: 'NetCDF',
  display: 'NetCDF license',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NetSNMPLicense: Coding = {
  code: 'Net-SNMP',
  display: 'Net-SNMP License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NewsletrLicense: Coding = {
  code: 'Newsletr',
  display: 'Newsletr License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NethackGeneralPublicLicense: Coding = {
  code: 'NGPL',
  display: 'Nethack General Public License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NorwegianLicenceForOpenGovernmentData: Coding = {
  code: 'NLOD-1.0',
  display: 'Norwegian Licence for Open Government Data',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NoLimitPublicLicense: Coding = {
  code: 'NLPL',
  display: 'No Limit Public License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NokiaOpenSourceLicense: Coding = {
  code: 'Nokia',
  display: 'Nokia Open Source License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NetizenOpenSourceLicense: Coding = {
  code: 'NOSL',
  display: 'Netizen Open Source License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NotOpenSource: Coding = {
  code: 'not-open-source',
  display: 'Not open source',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NowebLicense: Coding = {
  code: 'Noweb',
  display: 'Noweb License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NetscapePublicLicenseV10: Coding = {
  code: 'NPL-1.0',
  display: 'Netscape Public License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NetscapePublicLicenseV11: Coding = {
  code: 'NPL-1.1',
  display: 'Netscape Public License v1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NonProfitOpenSoftwareLicense30: Coding = {
  code: 'NPOSL-3.0',
  display: 'Non-Profit Open Software License 3.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NRLLicense: Coding = {
  code: 'NRL',
  display: 'NRL License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_NTPLicense: Coding = {
  code: 'NTP',
  display: 'NTP License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenCASCADETechnologyPublicLicense: Coding = {
  code: 'OCCT-PL',
  display: 'Open CASCADE Technology Public License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OCLCResearchPublicLicense20: Coding = {
  code: 'OCLC-2.0',
  display: 'OCLC Research Public License 2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ODCOpenDatabaseLicenseV10: Coding = {
  code: 'ODbL-1.0',
  display: 'ODC Open Database License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SILOpenFontLicense10: Coding = {
  code: 'OFL-1.0',
  display: 'SIL Open Font License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SILOpenFontLicense11: Coding = {
  code: 'OFL-1.1',
  display: 'SIL Open Font License 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenGroupTestSuiteLicense: Coding = {
  code: 'OGTSL',
  display: 'Open Group Test Suite License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV11: Coding = {
  code: 'OLDAP-1.1',
  display: 'Open LDAP Public License v1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV12: Coding = {
  code: 'OLDAP-1.2',
  display: 'Open LDAP Public License v1.2',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV13: Coding = {
  code: 'OLDAP-1.3',
  display: 'Open LDAP Public License v1.3',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV14: Coding = {
  code: 'OLDAP-1.4',
  display: 'Open LDAP Public License v1.4',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV20OrPossibly20AAnd20B: Coding = {
  code: 'OLDAP-2.0',
  display: 'Open LDAP Public License v2.0 (or possibly 2.0A and 2.0B)',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV201: Coding = {
  code: 'OLDAP-2.0.1',
  display: 'Open LDAP Public License v2.0.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV21: Coding = {
  code: 'OLDAP-2.1',
  display: 'Open LDAP Public License v2.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV22: Coding = {
  code: 'OLDAP-2.2',
  display: 'Open LDAP Public License v2.2',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV221: Coding = {
  code: 'OLDAP-2.2.1',
  display: 'Open LDAP Public License v2.2.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicense222: Coding = {
  code: 'OLDAP-2.2.2',
  display: 'Open LDAP Public License 2.2.2',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV23: Coding = {
  code: 'OLDAP-2.3',
  display: 'Open LDAP Public License v2.3',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV24: Coding = {
  code: 'OLDAP-2.4',
  display: 'Open LDAP Public License v2.4',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV25: Coding = {
  code: 'OLDAP-2.5',
  display: 'Open LDAP Public License v2.5',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV26: Coding = {
  code: 'OLDAP-2.6',
  display: 'Open LDAP Public License v2.6',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV27: Coding = {
  code: 'OLDAP-2.7',
  display: 'Open LDAP Public License v2.7',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenLDAPPublicLicenseV28: Coding = {
  code: 'OLDAP-2.8',
  display: 'Open LDAP Public License v2.8',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenMarketLicense: Coding = {
  code: 'OML',
  display: 'Open Market License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenSSLLicense: Coding = {
  code: 'OpenSSL',
  display: 'OpenSSL License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenPublicLicenseV10: Coding = {
  code: 'OPL-1.0',
  display: 'Open Public License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OSETPublicLicenseVersion21: Coding = {
  code: 'OSET-PL-2.1',
  display: 'OSET Public License version 2.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenSoftwareLicense10: Coding = {
  code: 'OSL-1.0',
  display: 'Open Software License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenSoftwareLicense11: Coding = {
  code: 'OSL-1.1',
  display: 'Open Software License 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenSoftwareLicense20: Coding = {
  code: 'OSL-2.0',
  display: 'Open Software License 2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenSoftwareLicense21: Coding = {
  code: 'OSL-2.1',
  display: 'Open Software License 2.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_OpenSoftwareLicense30: Coding = {
  code: 'OSL-3.0',
  display: 'Open Software License 3.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ODCPublicDomainDedicationAndLicense10: Coding = {
  code: 'PDDL-1.0',
  display: 'ODC Public Domain Dedication & License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_PHPLicenseV30: Coding = {
  code: 'PHP-3.0',
  display: 'PHP License v3.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_PHPLicenseV301: Coding = {
  code: 'PHP-3.01',
  display: 'PHP License v3.01',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_PlexusClassworldsLicense: Coding = {
  code: 'Plexus',
  display: 'Plexus Classworlds License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_PostgreSQLLicense: Coding = {
  code: 'PostgreSQL',
  display: 'PostgreSQL License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_PsfragLicense: Coding = {
  code: 'psfrag',
  display: 'psfrag License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_PsutilsLicense: Coding = {
  code: 'psutils',
  display: 'psutils License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_PythonLicense20: Coding = {
  code: 'Python-2.0',
  display: 'Python License 2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_QhullLicense: Coding = {
  code: 'Qhull',
  display: 'Qhull License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_QPublicLicense10: Coding = {
  code: 'QPL-1.0',
  display: 'Q Public License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_RdiscLicense: Coding = {
  code: 'Rdisc',
  display: 'Rdisc License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_RedHatECosPublicLicenseV11: Coding = {
  code: 'RHeCos-1.1',
  display: 'Red Hat eCos Public License v1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ReciprocalPublicLicense11: Coding = {
  code: 'RPL-1.1',
  display: 'Reciprocal Public License 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ReciprocalPublicLicense15: Coding = {
  code: 'RPL-1.5',
  display: 'Reciprocal Public License 1.5',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_RealNetworksPublicSourceLicenseV10: Coding = {
  code: 'RPSL-1.0',
  display: 'RealNetworks Public Source License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_RSAMessageDigestLicense: Coding = {
  code: 'RSA-MD',
  display: 'RSA Message-Digest License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_RicohSourceCodePublicLicense: Coding = {
  code: 'RSCPL',
  display: 'Ricoh Source Code Public License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_RubyLicense: Coding = {
  code: 'Ruby',
  display: 'Ruby License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SaxpathLicense: Coding = {
  code: 'Saxpath',
  display: 'Saxpath License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SaxPublicDomainNotice: Coding = {
  code: 'SAX-PD',
  display: 'Sax Public Domain Notice',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SCEASharedSourceLicense: Coding = {
  code: 'SCEA',
  display: 'SCEA Shared Source License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SendmailLicense: Coding = {
  code: 'Sendmail',
  display: 'Sendmail License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SGIFreeSoftwareLicenseBV10: Coding = {
  code: 'SGI-B-1.0',
  display: 'SGI Free Software License B v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SGIFreeSoftwareLicenseBV11: Coding = {
  code: 'SGI-B-1.1',
  display: 'SGI Free Software License B v1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SGIFreeSoftwareLicenseBV20: Coding = {
  code: 'SGI-B-2.0',
  display: 'SGI Free Software License B v2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SimplePublicLicense20: Coding = {
  code: 'SimPL-2.0',
  display: 'Simple Public License 2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SunIndustryStandardsSourceLicenseV11: Coding = {
  code: 'SISSL',
  display: 'Sun Industry Standards Source License v1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SunIndustryStandardsSourceLicenseV12: Coding = {
  code: 'SISSL-1.2',
  display: 'Sun Industry Standards Source License v1.2',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SleepycatLicense: Coding = {
  code: 'Sleepycat',
  display: 'Sleepycat License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_StandardMLOfNewJerseyLicense: Coding = {
  code: 'SMLNJ',
  display: 'Standard ML of New Jersey License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SecureMessagingProtocolPublicLicense: Coding = {
  code: 'SMPPL',
  display: 'Secure Messaging Protocol Public License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SNIAPublicLicense11: Coding = {
  code: 'SNIA',
  display: 'SNIA Public License 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SpencerLicense86: Coding = {
  code: 'Spencer-86',
  display: 'Spencer License 86',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SpencerLicense94: Coding = {
  code: 'Spencer-94',
  display: 'Spencer License 94',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SpencerLicense99: Coding = {
  code: 'Spencer-99',
  display: 'Spencer License 99',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SunPublicLicenseV10: Coding = {
  code: 'SPL-1.0',
  display: 'Sun Public License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SugarCRMPublicLicenseV113: Coding = {
  code: 'SugarCRM-1.1.3',
  display: 'SugarCRM Public License v1.1.3',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SchemeWidgetLibrarySWLSoftwareLicenseAgreement: Coding = {
  code: 'SWL',
  display: 'Scheme Widget Library (SWL) Software License Agreement',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_TCLTKLicense: Coding = {
  code: 'TCL',
  display: 'TCL/TK License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_TCPWrappersLicense: Coding = {
  code: 'TCP-wrappers',
  display: 'TCP Wrappers License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_TMateOpenSourceLicense: Coding = {
  code: 'TMate',
  display: 'TMate Open Source License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_TORQUEV25PlusSoftwareLicenseV11: Coding = {
  code: 'TORQUE-1.1',
  display: 'TORQUE v2.5+ Software License v1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_TrussterOpenSourceLicense: Coding = {
  code: 'TOSL',
  display: 'Trusster Open Source License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_UnicodeLicenseAgreementDataFilesAndSoftware2015: Coding = {
  code: 'Unicode-DFS-2015',
  display: 'Unicode License Agreement - Data Files and Software (2015)',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_UnicodeLicenseAgreementDataFilesAndSoftware2016: Coding = {
  code: 'Unicode-DFS-2016',
  display: 'Unicode License Agreement - Data Files and Software (2016)',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_UnicodeTermsOfUse: Coding = {
  code: 'Unicode-TOU',
  display: 'Unicode Terms of Use',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_TheUnlicense: Coding = {
  code: 'Unlicense',
  display: 'The Unlicense',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_UniversalPermissiveLicenseV10: Coding = {
  code: 'UPL-1.0',
  display: 'Universal Permissive License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_VimLicense: Coding = {
  code: 'Vim',
  display: 'Vim License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_VOSTROMPublicLicenseForOpenSource: Coding = {
  code: 'VOSTROM',
  display: 'VOSTROM Public License for Open Source',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_VovidaSoftwareLicenseV10: Coding = {
  code: 'VSL-1.0',
  display: 'Vovida Software License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_W3CSoftwareNoticeAndLicense20021231: Coding = {
  code: 'W3C',
  display: 'W3C Software Notice and License (2002-12-31)',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_W3CSoftwareNoticeAndLicense19980720: Coding = {
  code: 'W3C-19980720',
  display: 'W3C Software Notice and License (1998-07-20)',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_W3CSoftwareNoticeAndDocumentLicense20150513: Coding = {
  code: 'W3C-20150513',
  display: 'W3C Software Notice and Document License (2015-05-13)',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_SybaseOpenWatcomPublicLicense10: Coding = {
  code: 'Watcom-1.0',
  display: 'Sybase Open Watcom Public License 1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_WsuipaLicense: Coding = {
  code: 'Wsuipa',
  display: 'Wsuipa License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_DoWhatTheFAsteriskCkYouWantToPublicLicense: Coding = {
  code: 'WTFPL',
  display: 'Do What The F*ck You Want To Public License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_X11License: Coding = {
  code: 'X11',
  display: 'X11 License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_XeroxLicense: Coding = {
  code: 'Xerox',
  display: 'Xerox License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_XFree86License11: Coding = {
  code: 'XFree86-1.1',
  display: 'XFree86 License 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_XinetdLicense: Coding = {
  code: 'xinetd',
  display: 'xinetd License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_XNetLicense: Coding = {
  code: 'Xnet',
  display: 'X.Net License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_XPPLicense: Coding = {
  code: 'xpp',
  display: 'XPP License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_XSkatLicense: Coding = {
  code: 'XSkat',
  display: 'XSkat License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_YahooNotPublicLicenseV10: Coding = {
  code: 'YPL-1.0',
  display: 'Yahoo! Public License v1.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_YahooNotPublicLicenseV11: Coding = {
  code: 'YPL-1.1',
  display: 'Yahoo! Public License v1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ZedLicense: Coding = {
  code: 'Zed',
  display: 'Zed License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ZendLicenseV20: Coding = {
  code: 'Zend-2.0',
  display: 'Zend License v2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ZimbraPublicLicenseV13: Coding = {
  code: 'Zimbra-1.3',
  display: 'Zimbra Public License v1.3',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ZimbraPublicLicenseV14: Coding = {
  code: 'Zimbra-1.4',
  display: 'Zimbra Public License v1.4',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ZlibLicense: Coding = {
  code: 'Zlib',
  display: 'zlib License',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ZlibLibpngLicenseWithAcknowledgement: Coding = {
  code: 'zlib-acknowledgement',
  display: 'zlib/libpng License with Acknowledgement',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ZopePublicLicense11: Coding = {
  code: 'ZPL-1.1',
  display: 'Zope Public License 1.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ZopePublicLicense20: Coding = {
  code: 'ZPL-2.0',
  display: 'Zope Public License 2.0',
  system: 'http://hl7.org/fhir/spdx-license',
};
const spdx_license_ZopePublicLicense21: Coding = {
  code: 'ZPL-2.1',
  display: 'Zope Public License 2.1',
  system: 'http://hl7.org/fhir/spdx-license',
};
/**
 * The license that applies to an Implementation Guide (using an SPDX license Identifiers, or 'not-open-source'). The binding is required but new SPDX license Identifiers are allowed to be used (https://spdx.org/licenses/).
 */
export const SpdxLicense = {
  /**
   * BSD Zero Clause License.
   */
  BSDZeroClauseLicense: spdx_license_BSDZeroClauseLicense,
  /**
   * Attribution Assurance License.
   */
  AttributionAssuranceLicense: spdx_license_AttributionAssuranceLicense,
  /**
   * Abstyles License.
   */
  AbstylesLicense: spdx_license_AbstylesLicense,
  /**
   * Adobe Systems Incorporated Source Code License Agreement.
   */
  AdobeSystemsIncorporatedSourceCodeLicenseAgreement: spdx_license_AdobeSystemsIncorporatedSourceCodeLicenseAgreement,
  /**
   * Adobe Glyph List License.
   */
  AdobeGlyphListLicense: spdx_license_AdobeGlyphListLicense,
  /**
   * Amazon Digital Services License.
   */
  AmazonDigitalServicesLicense: spdx_license_AmazonDigitalServicesLicense,
  /**
   * Academic Free License v1.1.
   */
  AcademicFreeLicenseV11: spdx_license_AcademicFreeLicenseV11,
  /**
   * Academic Free License v1.2.
   */
  AcademicFreeLicenseV12: spdx_license_AcademicFreeLicenseV12,
  /**
   * Academic Free License v2.0.
   */
  AcademicFreeLicenseV20: spdx_license_AcademicFreeLicenseV20,
  /**
   * Academic Free License v2.1.
   */
  AcademicFreeLicenseV21: spdx_license_AcademicFreeLicenseV21,
  /**
   * Academic Free License v3.0.
   */
  AcademicFreeLicenseV30: spdx_license_AcademicFreeLicenseV30,
  /**
   * Afmparse License.
   */
  AfmparseLicense: spdx_license_AfmparseLicense,
  /**
   * Affero General Public License v1.0 only.
   */
  AfferoGeneralPublicLicenseV10Only: spdx_license_AfferoGeneralPublicLicenseV10Only,
  /**
   * Affero General Public License v1.0 or later.
   */
  AfferoGeneralPublicLicenseV10OrLater: spdx_license_AfferoGeneralPublicLicenseV10OrLater,
  /**
   * GNU Affero General Public License v3.0 only.
   */
  GNUAfferoGeneralPublicLicenseV30Only: spdx_license_GNUAfferoGeneralPublicLicenseV30Only,
  /**
   * GNU Affero General Public License v3.0 or later.
   */
  GNUAfferoGeneralPublicLicenseV30OrLater: spdx_license_GNUAfferoGeneralPublicLicenseV30OrLater,
  /**
   * Aladdin Free Public License.
   */
  AladdinFreePublicLicense: spdx_license_AladdinFreePublicLicense,
  /**
   * AMD's plpa_map.c License.
   */
  AMDQuoteSPlpaMapCLicense: spdx_license_AMDQuoteSPlpaMapCLicense,
  /**
   * Apple MIT License.
   */
  AppleMITLicense: spdx_license_AppleMITLicense,
  /**
   * Academy of Motion Picture Arts and Sciences BSD.
   */
  AcademyOfMotionPictureArtsAndSciencesBSD: spdx_license_AcademyOfMotionPictureArtsAndSciencesBSD,
  /**
   * ANTLR Software Rights Notice.
   */
  ANTLRSoftwareRightsNotice: spdx_license_ANTLRSoftwareRightsNotice,
  /**
   * Apache License 1.0.
   */
  ApacheLicense10: spdx_license_ApacheLicense10,
  /**
   * Apache License 1.1.
   */
  ApacheLicense11: spdx_license_ApacheLicense11,
  /**
   * Apache License 2.0.
   */
  ApacheLicense20: spdx_license_ApacheLicense20,
  /**
   * Adobe Postscript AFM License.
   */
  AdobePostscriptAFMLicense: spdx_license_AdobePostscriptAFMLicense,
  /**
   * Adaptive Public License 1.0.
   */
  AdaptivePublicLicense10: spdx_license_AdaptivePublicLicense10,
  /**
   * Apple Public Source License 1.0.
   */
  ApplePublicSourceLicense10: spdx_license_ApplePublicSourceLicense10,
  /**
   * Apple Public Source License 1.1.
   */
  ApplePublicSourceLicense11: spdx_license_ApplePublicSourceLicense11,
  /**
   * Apple Public Source License 1.2.
   */
  ApplePublicSourceLicense12: spdx_license_ApplePublicSourceLicense12,
  /**
   * Apple Public Source License 2.0.
   */
  ApplePublicSourceLicense20: spdx_license_ApplePublicSourceLicense20,
  /**
   * Artistic License 1.0.
   */
  ArtisticLicense10: spdx_license_ArtisticLicense10,
  /**
   * Artistic License 1.0 w/clause 8.
   */
  ArtisticLicense10WClause8: spdx_license_ArtisticLicense10WClause8,
  /**
   * Artistic License 1.0 (Perl).
   */
  ArtisticLicense10Perl: spdx_license_ArtisticLicense10Perl,
  /**
   * Artistic License 2.0.
   */
  ArtisticLicense20: spdx_license_ArtisticLicense20,
  /**
   * Bahyph License.
   */
  BahyphLicense: spdx_license_BahyphLicense,
  /**
   * Barr License.
   */
  BarrLicense: spdx_license_BarrLicense,
  /**
   * Beerware License.
   */
  BeerwareLicense: spdx_license_BeerwareLicense,
  /**
   * BitTorrent Open Source License v1.0.
   */
  BitTorrentOpenSourceLicenseV10: spdx_license_BitTorrentOpenSourceLicenseV10,
  /**
   * BitTorrent Open Source License v1.1.
   */
  BitTorrentOpenSourceLicenseV11: spdx_license_BitTorrentOpenSourceLicenseV11,
  /**
   * Borceux license.
   */
  BorceuxLicense: spdx_license_BorceuxLicense,
  /**
   * BSD 1-Clause License.
   */
  BSD1ClauseLicense: spdx_license_BSD1ClauseLicense,
  /**
   * BSD 2-Clause "Simplified" License.
   */
  BSD2ClauseQuotationSimplifiedQuotationLicense: spdx_license_BSD2ClauseQuotationSimplifiedQuotationLicense,
  /**
   * BSD 2-Clause FreeBSD License.
   */
  BSD2ClauseFreeBSDLicense: spdx_license_BSD2ClauseFreeBSDLicense,
  /**
   * BSD 2-Clause NetBSD License.
   */
  BSD2ClauseNetBSDLicense: spdx_license_BSD2ClauseNetBSDLicense,
  /**
   * BSD-2-Clause Plus Patent License.
   */
  BSD2ClausePlusPatentLicense: spdx_license_BSD2ClausePlusPatentLicense,
  /**
   * BSD 3-Clause "New" or "Revised" License.
   */
  BSD3ClauseQuotationNewQuotationOrQuotationRevisedQuotationLicense: spdx_license_BSD3ClauseQuotationNewQuotationOrQuotationRevisedQuotationLicense,
  /**
   * BSD with attribution.
   */
  BSDWithAttribution: spdx_license_BSDWithAttribution,
  /**
   * BSD 3-Clause Clear License.
   */
  BSD3ClauseClearLicense: spdx_license_BSD3ClauseClearLicense,
  /**
   * Lawrence Berkeley National Labs BSD variant license.
   */
  LawrenceBerkeleyNationalLabsBSDVariantLicense: spdx_license_LawrenceBerkeleyNationalLabsBSDVariantLicense,
  /**
   * BSD 3-Clause No Nuclear License.
   */
  BSD3ClauseNoNuclearLicense: spdx_license_BSD3ClauseNoNuclearLicense,
  /**
   * BSD 3-Clause No Nuclear License 2014.
   */
  BSD3ClauseNoNuclearLicense2014: spdx_license_BSD3ClauseNoNuclearLicense2014,
  /**
   * BSD 3-Clause No Nuclear Warranty.
   */
  BSD3ClauseNoNuclearWarranty: spdx_license_BSD3ClauseNoNuclearWarranty,
  /**
   * BSD 4-Clause "Original" or "Old" License.
   */
  BSD4ClauseQuotationOriginalQuotationOrQuotationOldQuotationLicense: spdx_license_BSD4ClauseQuotationOriginalQuotationOrQuotationOldQuotationLicense,
  /**
   * BSD-4-Clause (University of California-Specific).
   */
  BSD4ClauseUniversityOfCaliforniaSpecific: spdx_license_BSD4ClauseUniversityOfCaliforniaSpecific,
  /**
   * BSD Protection License.
   */
  BSDProtectionLicense: spdx_license_BSDProtectionLicense,
  /**
   * BSD Source Code Attribution.
   */
  BSDSourceCodeAttribution: spdx_license_BSDSourceCodeAttribution,
  /**
   * Boost Software License 1.0.
   */
  BoostSoftwareLicense10: spdx_license_BoostSoftwareLicense10,
  /**
   * bzip2 and libbzip2 License v1.0.5.
   */
  Bzip2AndLibbzip2LicenseV105: spdx_license_Bzip2AndLibbzip2LicenseV105,
  /**
   * bzip2 and libbzip2 License v1.0.6.
   */
  Bzip2AndLibbzip2LicenseV106: spdx_license_Bzip2AndLibbzip2LicenseV106,
  /**
   * Caldera License.
   */
  CalderaLicense: spdx_license_CalderaLicense,
  /**
   * Computer Associates Trusted Open Source License 1.1.
   */
  ComputerAssociatesTrustedOpenSourceLicense11: spdx_license_ComputerAssociatesTrustedOpenSourceLicense11,
  /**
   * Creative Commons Zero v1.0 Universal.
   */
  CreativeCommonsZeroV10Universal: spdx_license_CreativeCommonsZeroV10Universal,
  /**
   * Creative Commons Attribution 1.0 Generic.
   */
  CreativeCommonsAttribution10Generic: spdx_license_CreativeCommonsAttribution10Generic,
  /**
   * Creative Commons Attribution 2.0 Generic.
   */
  CreativeCommonsAttribution20Generic: spdx_license_CreativeCommonsAttribution20Generic,
  /**
   * Creative Commons Attribution 2.5 Generic.
   */
  CreativeCommonsAttribution25Generic: spdx_license_CreativeCommonsAttribution25Generic,
  /**
   * Creative Commons Attribution 3.0 Unported.
   */
  CreativeCommonsAttribution30Unported: spdx_license_CreativeCommonsAttribution30Unported,
  /**
   * Creative Commons Attribution 4.0 International.
   */
  CreativeCommonsAttribution40International: spdx_license_CreativeCommonsAttribution40International,
  /**
   * Creative Commons Attribution Non Commercial 1.0 Generic.
   */
  CreativeCommonsAttributionNonCommercial10Generic: spdx_license_CreativeCommonsAttributionNonCommercial10Generic,
  /**
   * Creative Commons Attribution Non Commercial 2.0 Generic.
   */
  CreativeCommonsAttributionNonCommercial20Generic: spdx_license_CreativeCommonsAttributionNonCommercial20Generic,
  /**
   * Creative Commons Attribution Non Commercial 2.5 Generic.
   */
  CreativeCommonsAttributionNonCommercial25Generic: spdx_license_CreativeCommonsAttributionNonCommercial25Generic,
  /**
   * Creative Commons Attribution Non Commercial 3.0 Unported.
   */
  CreativeCommonsAttributionNonCommercial30Unported: spdx_license_CreativeCommonsAttributionNonCommercial30Unported,
  /**
   * Creative Commons Attribution Non Commercial 4.0 International.
   */
  CreativeCommonsAttributionNonCommercial40International: spdx_license_CreativeCommonsAttributionNonCommercial40International,
  /**
   * Creative Commons Attribution Non Commercial No Derivatives 1.0 Generic.
   */
  CreativeCommonsAttributionNonCommercialNoDerivatives10Generic: spdx_license_CreativeCommonsAttributionNonCommercialNoDerivatives10Generic,
  /**
   * Creative Commons Attribution Non Commercial No Derivatives 2.0 Generic.
   */
  CreativeCommonsAttributionNonCommercialNoDerivatives20Generic: spdx_license_CreativeCommonsAttributionNonCommercialNoDerivatives20Generic,
  /**
   * Creative Commons Attribution Non Commercial No Derivatives 2.5 Generic.
   */
  CreativeCommonsAttributionNonCommercialNoDerivatives25Generic: spdx_license_CreativeCommonsAttributionNonCommercialNoDerivatives25Generic,
  /**
   * Creative Commons Attribution Non Commercial No Derivatives 3.0 Unported.
   */
  CreativeCommonsAttributionNonCommercialNoDerivatives30Unported: spdx_license_CreativeCommonsAttributionNonCommercialNoDerivatives30Unported,
  /**
   * Creative Commons Attribution Non Commercial No Derivatives 4.0 International.
   */
  CreativeCommonsAttributionNonCommercialNoDerivatives40International: spdx_license_CreativeCommonsAttributionNonCommercialNoDerivatives40International,
  /**
   * Creative Commons Attribution Non Commercial Share Alike 1.0 Generic.
   */
  CreativeCommonsAttributionNonCommercialShareAlike10Generic: spdx_license_CreativeCommonsAttributionNonCommercialShareAlike10Generic,
  /**
   * Creative Commons Attribution Non Commercial Share Alike 2.0 Generic.
   */
  CreativeCommonsAttributionNonCommercialShareAlike20Generic: spdx_license_CreativeCommonsAttributionNonCommercialShareAlike20Generic,
  /**
   * Creative Commons Attribution Non Commercial Share Alike 2.5 Generic.
   */
  CreativeCommonsAttributionNonCommercialShareAlike25Generic: spdx_license_CreativeCommonsAttributionNonCommercialShareAlike25Generic,
  /**
   * Creative Commons Attribution Non Commercial Share Alike 3.0 Unported.
   */
  CreativeCommonsAttributionNonCommercialShareAlike30Unported: spdx_license_CreativeCommonsAttributionNonCommercialShareAlike30Unported,
  /**
   * Creative Commons Attribution Non Commercial Share Alike 4.0 International.
   */
  CreativeCommonsAttributionNonCommercialShareAlike40International: spdx_license_CreativeCommonsAttributionNonCommercialShareAlike40International,
  /**
   * Creative Commons Attribution No Derivatives 1.0 Generic.
   */
  CreativeCommonsAttributionNoDerivatives10Generic: spdx_license_CreativeCommonsAttributionNoDerivatives10Generic,
  /**
   * Creative Commons Attribution No Derivatives 2.0 Generic.
   */
  CreativeCommonsAttributionNoDerivatives20Generic: spdx_license_CreativeCommonsAttributionNoDerivatives20Generic,
  /**
   * Creative Commons Attribution No Derivatives 2.5 Generic.
   */
  CreativeCommonsAttributionNoDerivatives25Generic: spdx_license_CreativeCommonsAttributionNoDerivatives25Generic,
  /**
   * Creative Commons Attribution No Derivatives 3.0 Unported.
   */
  CreativeCommonsAttributionNoDerivatives30Unported: spdx_license_CreativeCommonsAttributionNoDerivatives30Unported,
  /**
   * Creative Commons Attribution No Derivatives 4.0 International.
   */
  CreativeCommonsAttributionNoDerivatives40International: spdx_license_CreativeCommonsAttributionNoDerivatives40International,
  /**
   * Creative Commons Attribution Share Alike 1.0 Generic.
   */
  CreativeCommonsAttributionShareAlike10Generic: spdx_license_CreativeCommonsAttributionShareAlike10Generic,
  /**
   * Creative Commons Attribution Share Alike 2.0 Generic.
   */
  CreativeCommonsAttributionShareAlike20Generic: spdx_license_CreativeCommonsAttributionShareAlike20Generic,
  /**
   * Creative Commons Attribution Share Alike 2.5 Generic.
   */
  CreativeCommonsAttributionShareAlike25Generic: spdx_license_CreativeCommonsAttributionShareAlike25Generic,
  /**
   * Creative Commons Attribution Share Alike 3.0 Unported.
   */
  CreativeCommonsAttributionShareAlike30Unported: spdx_license_CreativeCommonsAttributionShareAlike30Unported,
  /**
   * Creative Commons Attribution Share Alike 4.0 International.
   */
  CreativeCommonsAttributionShareAlike40International: spdx_license_CreativeCommonsAttributionShareAlike40International,
  /**
   * Common Development and Distribution License 1.0.
   */
  CommonDevelopmentAndDistributionLicense10: spdx_license_CommonDevelopmentAndDistributionLicense10,
  /**
   * Common Development and Distribution License 1.1.
   */
  CommonDevelopmentAndDistributionLicense11: spdx_license_CommonDevelopmentAndDistributionLicense11,
  /**
   * Community Data License Agreement Permissive 1.0.
   */
  CommunityDataLicenseAgreementPermissive10: spdx_license_CommunityDataLicenseAgreementPermissive10,
  /**
   * Community Data License Agreement Sharing 1.0.
   */
  CommunityDataLicenseAgreementSharing10: spdx_license_CommunityDataLicenseAgreementSharing10,
  /**
   * CeCILL Free Software License Agreement v1.0.
   */
  CeCILLFreeSoftwareLicenseAgreementV10: spdx_license_CeCILLFreeSoftwareLicenseAgreementV10,
  /**
   * CeCILL Free Software License Agreement v1.1.
   */
  CeCILLFreeSoftwareLicenseAgreementV11: spdx_license_CeCILLFreeSoftwareLicenseAgreementV11,
  /**
   * CeCILL Free Software License Agreement v2.0.
   */
  CeCILLFreeSoftwareLicenseAgreementV20: spdx_license_CeCILLFreeSoftwareLicenseAgreementV20,
  /**
   * CeCILL Free Software License Agreement v2.1.
   */
  CeCILLFreeSoftwareLicenseAgreementV21: spdx_license_CeCILLFreeSoftwareLicenseAgreementV21,
  /**
   * CeCILL-B Free Software License Agreement.
   */
  CeCILLBFreeSoftwareLicenseAgreement: spdx_license_CeCILLBFreeSoftwareLicenseAgreement,
  /**
   * CeCILL-C Free Software License Agreement.
   */
  CeCILLCFreeSoftwareLicenseAgreement: spdx_license_CeCILLCFreeSoftwareLicenseAgreement,
  /**
   * Clarified Artistic License.
   */
  ClarifiedArtisticLicense: spdx_license_ClarifiedArtisticLicense,
  /**
   * CNRI Jython License.
   */
  CNRIJythonLicense: spdx_license_CNRIJythonLicense,
  /**
   * CNRI Python License.
   */
  CNRIPythonLicense: spdx_license_CNRIPythonLicense,
  /**
   * CNRI Python Open Source GPL Compatible License Agreement.
   */
  CNRIPythonOpenSourceGPLCompatibleLicenseAgreement: spdx_license_CNRIPythonOpenSourceGPLCompatibleLicenseAgreement,
  /**
   * Condor Public License v1.1.
   */
  CondorPublicLicenseV11: spdx_license_CondorPublicLicenseV11,
  /**
   * Common Public Attribution License 1.0.
   */
  CommonPublicAttributionLicense10: spdx_license_CommonPublicAttributionLicense10,
  /**
   * Common Public License 1.0.
   */
  CommonPublicLicense10: spdx_license_CommonPublicLicense10,
  /**
   * Code Project Open License 1.02.
   */
  CodeProjectOpenLicense102: spdx_license_CodeProjectOpenLicense102,
  /**
   * Crossword License.
   */
  CrosswordLicense: spdx_license_CrosswordLicense,
  /**
   * CrystalStacker License.
   */
  CrystalStackerLicense: spdx_license_CrystalStackerLicense,
  /**
   * CUA Office Public License v1.0.
   */
  CUAOfficePublicLicenseV10: spdx_license_CUAOfficePublicLicenseV10,
  /**
   * Cube License.
   */
  CubeLicense: spdx_license_CubeLicense,
  /**
   * curl License.
   */
  CurlLicense: spdx_license_CurlLicense,
  /**
   * Deutsche Freie Software Lizenz.
   */
  DeutscheFreieSoftwareLizenz: spdx_license_DeutscheFreieSoftwareLizenz,
  /**
   * diffmark license.
   */
  DiffmarkLicense: spdx_license_DiffmarkLicense,
  /**
   * DOC License.
   */
  DOCLicense: spdx_license_DOCLicense,
  /**
   * Dotseqn License.
   */
  DotseqnLicense: spdx_license_DotseqnLicense,
  /**
   * DSDP License.
   */
  DSDPLicense: spdx_license_DSDPLicense,
  /**
   * dvipdfm License.
   */
  DvipdfmLicense: spdx_license_DvipdfmLicense,
  /**
   * Educational Community License v1.0.
   */
  EducationalCommunityLicenseV10: spdx_license_EducationalCommunityLicenseV10,
  /**
   * Educational Community License v2.0.
   */
  EducationalCommunityLicenseV20: spdx_license_EducationalCommunityLicenseV20,
  /**
   * Eiffel Forum License v1.0.
   */
  EiffelForumLicenseV10: spdx_license_EiffelForumLicenseV10,
  /**
   * Eiffel Forum License v2.0.
   */
  EiffelForumLicenseV20: spdx_license_EiffelForumLicenseV20,
  /**
   * eGenix.com Public License 1.1.0.
   */
  EGenixComPublicLicense110: spdx_license_EGenixComPublicLicense110,
  /**
   * Entessa Public License v1.0.
   */
  EntessaPublicLicenseV10: spdx_license_EntessaPublicLicenseV10,
  /**
   * Eclipse Public License 1.0.
   */
  EclipsePublicLicense10: spdx_license_EclipsePublicLicense10,
  /**
   * Eclipse Public License 2.0.
   */
  EclipsePublicLicense20: spdx_license_EclipsePublicLicense20,
  /**
   * Erlang Public License v1.1.
   */
  ErlangPublicLicenseV11: spdx_license_ErlangPublicLicenseV11,
  /**
   * EU DataGrid Software License.
   */
  EUDataGridSoftwareLicense: spdx_license_EUDataGridSoftwareLicense,
  /**
   * European Union Public License 1.0.
   */
  EuropeanUnionPublicLicense10: spdx_license_EuropeanUnionPublicLicense10,
  /**
   * European Union Public License 1.1.
   */
  EuropeanUnionPublicLicense11: spdx_license_EuropeanUnionPublicLicense11,
  /**
   * European Union Public License 1.2.
   */
  EuropeanUnionPublicLicense12: spdx_license_EuropeanUnionPublicLicense12,
  /**
   * Eurosym License.
   */
  EurosymLicense: spdx_license_EurosymLicense,
  /**
   * Fair License.
   */
  FairLicense: spdx_license_FairLicense,
  /**
   * Frameworx Open License 1.0.
   */
  FrameworxOpenLicense10: spdx_license_FrameworxOpenLicense10,
  /**
   * FreeImage Public License v1.0.
   */
  FreeImagePublicLicenseV10: spdx_license_FreeImagePublicLicenseV10,
  /**
   * FSF All Permissive License.
   */
  FSFAllPermissiveLicense: spdx_license_FSFAllPermissiveLicense,
  /**
   * FSF Unlimited License.
   */
  FSFUnlimitedLicense: spdx_license_FSFUnlimitedLicense,
  /**
   * FSF Unlimited License (with License Retention).
   */
  FSFUnlimitedLicenseWithLicenseRetention: spdx_license_FSFUnlimitedLicenseWithLicenseRetention,
  /**
   * Freetype Project License.
   */
  FreetypeProjectLicense: spdx_license_FreetypeProjectLicense,
  /**
   * GNU Free Documentation License v1.1 only.
   */
  GNUFreeDocumentationLicenseV11Only: spdx_license_GNUFreeDocumentationLicenseV11Only,
  /**
   * GNU Free Documentation License v1.1 or later.
   */
  GNUFreeDocumentationLicenseV11OrLater: spdx_license_GNUFreeDocumentationLicenseV11OrLater,
  /**
   * GNU Free Documentation License v1.2 only.
   */
  GNUFreeDocumentationLicenseV12Only: spdx_license_GNUFreeDocumentationLicenseV12Only,
  /**
   * GNU Free Documentation License v1.2 or later.
   */
  GNUFreeDocumentationLicenseV12OrLater: spdx_license_GNUFreeDocumentationLicenseV12OrLater,
  /**
   * GNU Free Documentation License v1.3 only.
   */
  GNUFreeDocumentationLicenseV13Only: spdx_license_GNUFreeDocumentationLicenseV13Only,
  /**
   * GNU Free Documentation License v1.3 or later.
   */
  GNUFreeDocumentationLicenseV13OrLater: spdx_license_GNUFreeDocumentationLicenseV13OrLater,
  /**
   * Giftware License.
   */
  GiftwareLicense: spdx_license_GiftwareLicense,
  /**
   * GL2PS License.
   */
  GL2PSLicense: spdx_license_GL2PSLicense,
  /**
   * 3dfx Glide License.
   */
  VAL3dfxGlideLicense: spdx_license_VAL3dfxGlideLicense,
  /**
   * Glulxe License.
   */
  GlulxeLicense: spdx_license_GlulxeLicense,
  /**
   * gnuplot License.
   */
  GnuplotLicense: spdx_license_GnuplotLicense,
  /**
   * GNU General Public License v1.0 only.
   */
  GNUGeneralPublicLicenseV10Only: spdx_license_GNUGeneralPublicLicenseV10Only,
  /**
   * GNU General Public License v1.0 or later.
   */
  GNUGeneralPublicLicenseV10OrLater: spdx_license_GNUGeneralPublicLicenseV10OrLater,
  /**
   * GNU General Public License v2.0 only.
   */
  GNUGeneralPublicLicenseV20Only: spdx_license_GNUGeneralPublicLicenseV20Only,
  /**
   * GNU General Public License v2.0 or later.
   */
  GNUGeneralPublicLicenseV20OrLater: spdx_license_GNUGeneralPublicLicenseV20OrLater,
  /**
   * GNU General Public License v3.0 only.
   */
  GNUGeneralPublicLicenseV30Only: spdx_license_GNUGeneralPublicLicenseV30Only,
  /**
   * GNU General Public License v3.0 or later.
   */
  GNUGeneralPublicLicenseV30OrLater: spdx_license_GNUGeneralPublicLicenseV30OrLater,
  /**
   * gSOAP Public License v1.3b.
   */
  GSOAPPublicLicenseV13b: spdx_license_GSOAPPublicLicenseV13b,
  /**
   * Haskell Language Report License.
   */
  HaskellLanguageReportLicense: spdx_license_HaskellLanguageReportLicense,
  /**
   * Historical Permission Notice and Disclaimer.
   */
  HistoricalPermissionNoticeAndDisclaimer: spdx_license_HistoricalPermissionNoticeAndDisclaimer,
  /**
   * IBM PowerPC Initialization and Boot Software.
   */
  IBMPowerPCInitializationAndBootSoftware: spdx_license_IBMPowerPCInitializationAndBootSoftware,
  /**
   * ICU License.
   */
  ICULicense: spdx_license_ICULicense,
  /**
   * Independent JPEG Group License.
   */
  IndependentJPEGGroupLicense: spdx_license_IndependentJPEGGroupLicense,
  /**
   * ImageMagick License.
   */
  ImageMagickLicense: spdx_license_ImageMagickLicense,
  /**
   * iMatix Standard Function Library Agreement.
   */
  IMatixStandardFunctionLibraryAgreement: spdx_license_IMatixStandardFunctionLibraryAgreement,
  /**
   * Imlib2 License.
   */
  Imlib2License: spdx_license_Imlib2License,
  /**
   * Info-ZIP License.
   */
  InfoZIPLicense: spdx_license_InfoZIPLicense,
  /**
   * Intel Open Source License.
   */
  IntelOpenSourceLicense: spdx_license_IntelOpenSourceLicense,
  /**
   * Intel ACPI Software License Agreement.
   */
  IntelACPISoftwareLicenseAgreement: spdx_license_IntelACPISoftwareLicenseAgreement,
  /**
   * Interbase Public License v1.0.
   */
  InterbasePublicLicenseV10: spdx_license_InterbasePublicLicenseV10,
  /**
   * IPA Font License.
   */
  IPAFontLicense: spdx_license_IPAFontLicense,
  /**
   * IBM Public License v1.0.
   */
  IBMPublicLicenseV10: spdx_license_IBMPublicLicenseV10,
  /**
   * ISC License.
   */
  ISCLicense: spdx_license_ISCLicense,
  /**
   * JasPer License.
   */
  JasPerLicense: spdx_license_JasPerLicense,
  /**
   * JSON License.
   */
  JSONLicense: spdx_license_JSONLicense,
  /**
   * Licence Art Libre 1.2.
   */
  LicenceArtLibre12: spdx_license_LicenceArtLibre12,
  /**
   * Licence Art Libre 1.3.
   */
  LicenceArtLibre13: spdx_license_LicenceArtLibre13,
  /**
   * Latex2e License.
   */
  Latex2eLicense: spdx_license_Latex2eLicense,
  /**
   * Leptonica License.
   */
  LeptonicaLicense: spdx_license_LeptonicaLicense,
  /**
   * GNU Library General Public License v2 only.
   */
  GNULibraryGeneralPublicLicenseV2Only: spdx_license_GNULibraryGeneralPublicLicenseV2Only,
  /**
   * GNU Library General Public License v2 or later.
   */
  GNULibraryGeneralPublicLicenseV2OrLater: spdx_license_GNULibraryGeneralPublicLicenseV2OrLater,
  /**
   * GNU Lesser General Public License v2.1 only.
   */
  GNULesserGeneralPublicLicenseV21Only: spdx_license_GNULesserGeneralPublicLicenseV21Only,
  /**
   * GNU Lesser General Public License v2.1 or later.
   */
  GNULesserGeneralPublicLicenseV21OrLater: spdx_license_GNULesserGeneralPublicLicenseV21OrLater,
  /**
   * GNU Lesser General Public License v3.0 only.
   */
  GNULesserGeneralPublicLicenseV30Only: spdx_license_GNULesserGeneralPublicLicenseV30Only,
  /**
   * GNU Lesser General Public License v3.0 or later.
   */
  GNULesserGeneralPublicLicenseV30OrLater: spdx_license_GNULesserGeneralPublicLicenseV30OrLater,
  /**
   * Lesser General Public License For Linguistic Resources.
   */
  LesserGeneralPublicLicenseForLinguisticResources: spdx_license_LesserGeneralPublicLicenseForLinguisticResources,
  /**
   * libpng License.
   */
  LibpngLicense: spdx_license_LibpngLicense,
  /**
   * libtiff License.
   */
  LibtiffLicense: spdx_license_LibtiffLicense,
  /**
   * Licence Libre du Québec – Permissive version 1.1.
   */
  LicenceLibreDuQubecPermissiveVersion11: spdx_license_LicenceLibreDuQubecPermissiveVersion11,
  /**
   * Licence Libre du Québec – Réciprocité version 1.1.
   */
  LicenceLibreDuQubecRciprocitVersion11: spdx_license_LicenceLibreDuQubecRciprocitVersion11,
  /**
   * Licence Libre du Québec – Réciprocité forte version 1.1.
   */
  LicenceLibreDuQubecRciprocitForteVersion11: spdx_license_LicenceLibreDuQubecRciprocitForteVersion11,
  /**
   * Linux Kernel Variant of OpenIB.org license.
   */
  LinuxKernelVariantOfOpenIBOrgLicense: spdx_license_LinuxKernelVariantOfOpenIBOrgLicense,
  /**
   * Lucent Public License Version 1.0.
   */
  LucentPublicLicenseVersion10: spdx_license_LucentPublicLicenseVersion10,
  /**
   * Lucent Public License v1.02.
   */
  LucentPublicLicenseV102: spdx_license_LucentPublicLicenseV102,
  /**
   * LaTeX Project Public License v1.0.
   */
  LaTeXProjectPublicLicenseV10: spdx_license_LaTeXProjectPublicLicenseV10,
  /**
   * LaTeX Project Public License v1.1.
   */
  LaTeXProjectPublicLicenseV11: spdx_license_LaTeXProjectPublicLicenseV11,
  /**
   * LaTeX Project Public License v1.2.
   */
  LaTeXProjectPublicLicenseV12: spdx_license_LaTeXProjectPublicLicenseV12,
  /**
   * LaTeX Project Public License v1.3a.
   */
  LaTeXProjectPublicLicenseV13a: spdx_license_LaTeXProjectPublicLicenseV13a,
  /**
   * LaTeX Project Public License v1.3c.
   */
  LaTeXProjectPublicLicenseV13c: spdx_license_LaTeXProjectPublicLicenseV13c,
  /**
   * MakeIndex License.
   */
  MakeIndexLicense: spdx_license_MakeIndexLicense,
  /**
   * MirOS License.
   */
  MirOSLicense: spdx_license_MirOSLicense,
  /**
   * MIT License.
   */
  MITLicense: spdx_license_MITLicense,
  /**
   * MIT No Attribution.
   */
  MITNoAttribution: spdx_license_MITNoAttribution,
  /**
   * Enlightenment License (e16).
   */
  EnlightenmentLicenseE16: spdx_license_EnlightenmentLicenseE16,
  /**
   * CMU License.
   */
  CMULicense: spdx_license_CMULicense,
  /**
   * enna License.
   */
  EnnaLicense: spdx_license_EnnaLicense,
  /**
   * feh License.
   */
  FehLicense: spdx_license_FehLicense,
  /**
   * MIT +no-false-attribs license.
   */
  MITPlusNoFalseAttribsLicense: spdx_license_MITPlusNoFalseAttribsLicense,
  /**
   * Motosoto License.
   */
  MotosotoLicense: spdx_license_MotosotoLicense,
  /**
   * mpich2 License.
   */
  Mpich2License: spdx_license_Mpich2License,
  /**
   * Mozilla Public License 1.0.
   */
  MozillaPublicLicense10: spdx_license_MozillaPublicLicense10,
  /**
   * Mozilla Public License 1.1.
   */
  MozillaPublicLicense11: spdx_license_MozillaPublicLicense11,
  /**
   * Mozilla Public License 2.0.
   */
  MozillaPublicLicense20: spdx_license_MozillaPublicLicense20,
  /**
   * Mozilla Public License 2.0 (no copyleft exception).
   */
  MozillaPublicLicense20NoCopyleftException: spdx_license_MozillaPublicLicense20NoCopyleftException,
  /**
   * Microsoft Public License.
   */
  MicrosoftPublicLicense: spdx_license_MicrosoftPublicLicense,
  /**
   * Microsoft Reciprocal License.
   */
  MicrosoftReciprocalLicense: spdx_license_MicrosoftReciprocalLicense,
  /**
   * Matrix Template Library License.
   */
  MatrixTemplateLibraryLicense: spdx_license_MatrixTemplateLibraryLicense,
  /**
   * Multics License.
   */
  MulticsLicense: spdx_license_MulticsLicense,
  /**
   * Mup License.
   */
  MupLicense: spdx_license_MupLicense,
  /**
   * NASA Open Source Agreement 1.3.
   */
  NASAOpenSourceAgreement13: spdx_license_NASAOpenSourceAgreement13,
  /**
   * Naumen Public License.
   */
  NaumenPublicLicense: spdx_license_NaumenPublicLicense,
  /**
   * Net Boolean Public License v1.
   */
  NetBooleanPublicLicenseV1: spdx_license_NetBooleanPublicLicenseV1,
  /**
   * University of Illinois/NCSA Open Source License.
   */
  UniversityOfIllinoisNCSAOpenSourceLicense: spdx_license_UniversityOfIllinoisNCSAOpenSourceLicense,
  /**
   * NetCDF license.
   */
  NetCDFLicense: spdx_license_NetCDFLicense,
  /**
   * Net-SNMP License.
   */
  NetSNMPLicense: spdx_license_NetSNMPLicense,
  /**
   * Newsletr License.
   */
  NewsletrLicense: spdx_license_NewsletrLicense,
  /**
   * Nethack General Public License.
   */
  NethackGeneralPublicLicense: spdx_license_NethackGeneralPublicLicense,
  /**
   * Norwegian Licence for Open Government Data.
   */
  NorwegianLicenceForOpenGovernmentData: spdx_license_NorwegianLicenceForOpenGovernmentData,
  /**
   * No Limit Public License.
   */
  NoLimitPublicLicense: spdx_license_NoLimitPublicLicense,
  /**
   * Nokia Open Source License.
   */
  NokiaOpenSourceLicense: spdx_license_NokiaOpenSourceLicense,
  /**
   * Netizen Open Source License.
   */
  NetizenOpenSourceLicense: spdx_license_NetizenOpenSourceLicense,
  /**
   * Not an open source license.
   */
  NotOpenSource: spdx_license_NotOpenSource,
  /**
   * Noweb License.
   */
  NowebLicense: spdx_license_NowebLicense,
  /**
   * Netscape Public License v1.0.
   */
  NetscapePublicLicenseV10: spdx_license_NetscapePublicLicenseV10,
  /**
   * Netscape Public License v1.1.
   */
  NetscapePublicLicenseV11: spdx_license_NetscapePublicLicenseV11,
  /**
   * Non-Profit Open Software License 3.0.
   */
  NonProfitOpenSoftwareLicense30: spdx_license_NonProfitOpenSoftwareLicense30,
  /**
   * NRL License.
   */
  NRLLicense: spdx_license_NRLLicense,
  /**
   * NTP License.
   */
  NTPLicense: spdx_license_NTPLicense,
  /**
   * Open CASCADE Technology Public License.
   */
  OpenCASCADETechnologyPublicLicense: spdx_license_OpenCASCADETechnologyPublicLicense,
  /**
   * OCLC Research Public License 2.0.
   */
  OCLCResearchPublicLicense20: spdx_license_OCLCResearchPublicLicense20,
  /**
   * ODC Open Database License v1.0.
   */
  ODCOpenDatabaseLicenseV10: spdx_license_ODCOpenDatabaseLicenseV10,
  /**
   * SIL Open Font License 1.0.
   */
  SILOpenFontLicense10: spdx_license_SILOpenFontLicense10,
  /**
   * SIL Open Font License 1.1.
   */
  SILOpenFontLicense11: spdx_license_SILOpenFontLicense11,
  /**
   * Open Group Test Suite License.
   */
  OpenGroupTestSuiteLicense: spdx_license_OpenGroupTestSuiteLicense,
  /**
   * Open LDAP Public License v1.1.
   */
  OpenLDAPPublicLicenseV11: spdx_license_OpenLDAPPublicLicenseV11,
  /**
   * Open LDAP Public License v1.2.
   */
  OpenLDAPPublicLicenseV12: spdx_license_OpenLDAPPublicLicenseV12,
  /**
   * Open LDAP Public License v1.3.
   */
  OpenLDAPPublicLicenseV13: spdx_license_OpenLDAPPublicLicenseV13,
  /**
   * Open LDAP Public License v1.4.
   */
  OpenLDAPPublicLicenseV14: spdx_license_OpenLDAPPublicLicenseV14,
  /**
   * Open LDAP Public License v2.0 (or possibly 2.0A and 2.0B).
   */
  OpenLDAPPublicLicenseV20OrPossibly20AAnd20B: spdx_license_OpenLDAPPublicLicenseV20OrPossibly20AAnd20B,
  /**
   * Open LDAP Public License v2.0.1.
   */
  OpenLDAPPublicLicenseV201: spdx_license_OpenLDAPPublicLicenseV201,
  /**
   * Open LDAP Public License v2.1.
   */
  OpenLDAPPublicLicenseV21: spdx_license_OpenLDAPPublicLicenseV21,
  /**
   * Open LDAP Public License v2.2.
   */
  OpenLDAPPublicLicenseV22: spdx_license_OpenLDAPPublicLicenseV22,
  /**
   * Open LDAP Public License v2.2.1.
   */
  OpenLDAPPublicLicenseV221: spdx_license_OpenLDAPPublicLicenseV221,
  /**
   * Open LDAP Public License 2.2.2.
   */
  OpenLDAPPublicLicense222: spdx_license_OpenLDAPPublicLicense222,
  /**
   * Open LDAP Public License v2.3.
   */
  OpenLDAPPublicLicenseV23: spdx_license_OpenLDAPPublicLicenseV23,
  /**
   * Open LDAP Public License v2.4.
   */
  OpenLDAPPublicLicenseV24: spdx_license_OpenLDAPPublicLicenseV24,
  /**
   * Open LDAP Public License v2.5.
   */
  OpenLDAPPublicLicenseV25: spdx_license_OpenLDAPPublicLicenseV25,
  /**
   * Open LDAP Public License v2.6.
   */
  OpenLDAPPublicLicenseV26: spdx_license_OpenLDAPPublicLicenseV26,
  /**
   * Open LDAP Public License v2.7.
   */
  OpenLDAPPublicLicenseV27: spdx_license_OpenLDAPPublicLicenseV27,
  /**
   * Open LDAP Public License v2.8.
   */
  OpenLDAPPublicLicenseV28: spdx_license_OpenLDAPPublicLicenseV28,
  /**
   * Open Market License.
   */
  OpenMarketLicense: spdx_license_OpenMarketLicense,
  /**
   * OpenSSL License.
   */
  OpenSSLLicense: spdx_license_OpenSSLLicense,
  /**
   * Open Public License v1.0.
   */
  OpenPublicLicenseV10: spdx_license_OpenPublicLicenseV10,
  /**
   * OSET Public License version 2.1.
   */
  OSETPublicLicenseVersion21: spdx_license_OSETPublicLicenseVersion21,
  /**
   * Open Software License 1.0.
   */
  OpenSoftwareLicense10: spdx_license_OpenSoftwareLicense10,
  /**
   * Open Software License 1.1.
   */
  OpenSoftwareLicense11: spdx_license_OpenSoftwareLicense11,
  /**
   * Open Software License 2.0.
   */
  OpenSoftwareLicense20: spdx_license_OpenSoftwareLicense20,
  /**
   * Open Software License 2.1.
   */
  OpenSoftwareLicense21: spdx_license_OpenSoftwareLicense21,
  /**
   * Open Software License 3.0.
   */
  OpenSoftwareLicense30: spdx_license_OpenSoftwareLicense30,
  /**
   * ODC Public Domain Dedication & License 1.0.
   */
  ODCPublicDomainDedicationAndLicense10: spdx_license_ODCPublicDomainDedicationAndLicense10,
  /**
   * PHP License v3.0.
   */
  PHPLicenseV30: spdx_license_PHPLicenseV30,
  /**
   * PHP License v3.01.
   */
  PHPLicenseV301: spdx_license_PHPLicenseV301,
  /**
   * Plexus Classworlds License.
   */
  PlexusClassworldsLicense: spdx_license_PlexusClassworldsLicense,
  /**
   * PostgreSQL License.
   */
  PostgreSQLLicense: spdx_license_PostgreSQLLicense,
  /**
   * psfrag License.
   */
  PsfragLicense: spdx_license_PsfragLicense,
  /**
   * psutils License.
   */
  PsutilsLicense: spdx_license_PsutilsLicense,
  /**
   * Python License 2.0.
   */
  PythonLicense20: spdx_license_PythonLicense20,
  /**
   * Qhull License.
   */
  QhullLicense: spdx_license_QhullLicense,
  /**
   * Q Public License 1.0.
   */
  QPublicLicense10: spdx_license_QPublicLicense10,
  /**
   * Rdisc License.
   */
  RdiscLicense: spdx_license_RdiscLicense,
  /**
   * Red Hat eCos Public License v1.1.
   */
  RedHatECosPublicLicenseV11: spdx_license_RedHatECosPublicLicenseV11,
  /**
   * Reciprocal Public License 1.1.
   */
  ReciprocalPublicLicense11: spdx_license_ReciprocalPublicLicense11,
  /**
   * Reciprocal Public License 1.5.
   */
  ReciprocalPublicLicense15: spdx_license_ReciprocalPublicLicense15,
  /**
   * RealNetworks Public Source License v1.0.
   */
  RealNetworksPublicSourceLicenseV10: spdx_license_RealNetworksPublicSourceLicenseV10,
  /**
   * RSA Message-Digest License.
   */
  RSAMessageDigestLicense: spdx_license_RSAMessageDigestLicense,
  /**
   * Ricoh Source Code Public License.
   */
  RicohSourceCodePublicLicense: spdx_license_RicohSourceCodePublicLicense,
  /**
   * Ruby License.
   */
  RubyLicense: spdx_license_RubyLicense,
  /**
   * Saxpath License.
   */
  SaxpathLicense: spdx_license_SaxpathLicense,
  /**
   * Sax Public Domain Notice.
   */
  SaxPublicDomainNotice: spdx_license_SaxPublicDomainNotice,
  /**
   * SCEA Shared Source License.
   */
  SCEASharedSourceLicense: spdx_license_SCEASharedSourceLicense,
  /**
   * Sendmail License.
   */
  SendmailLicense: spdx_license_SendmailLicense,
  /**
   * SGI Free Software License B v1.0.
   */
  SGIFreeSoftwareLicenseBV10: spdx_license_SGIFreeSoftwareLicenseBV10,
  /**
   * SGI Free Software License B v1.1.
   */
  SGIFreeSoftwareLicenseBV11: spdx_license_SGIFreeSoftwareLicenseBV11,
  /**
   * SGI Free Software License B v2.0.
   */
  SGIFreeSoftwareLicenseBV20: spdx_license_SGIFreeSoftwareLicenseBV20,
  /**
   * Simple Public License 2.0.
   */
  SimplePublicLicense20: spdx_license_SimplePublicLicense20,
  /**
   * Sun Industry Standards Source License v1.1.
   */
  SunIndustryStandardsSourceLicenseV11: spdx_license_SunIndustryStandardsSourceLicenseV11,
  /**
   * Sun Industry Standards Source License v1.2.
   */
  SunIndustryStandardsSourceLicenseV12: spdx_license_SunIndustryStandardsSourceLicenseV12,
  /**
   * Sleepycat License.
   */
  SleepycatLicense: spdx_license_SleepycatLicense,
  /**
   * Standard ML of New Jersey License.
   */
  StandardMLOfNewJerseyLicense: spdx_license_StandardMLOfNewJerseyLicense,
  /**
   * Secure Messaging Protocol Public License.
   */
  SecureMessagingProtocolPublicLicense: spdx_license_SecureMessagingProtocolPublicLicense,
  /**
   * SNIA Public License 1.1.
   */
  SNIAPublicLicense11: spdx_license_SNIAPublicLicense11,
  /**
   * Spencer License 86.
   */
  SpencerLicense86: spdx_license_SpencerLicense86,
  /**
   * Spencer License 94.
   */
  SpencerLicense94: spdx_license_SpencerLicense94,
  /**
   * Spencer License 99.
   */
  SpencerLicense99: spdx_license_SpencerLicense99,
  /**
   * Sun Public License v1.0.
   */
  SunPublicLicenseV10: spdx_license_SunPublicLicenseV10,
  /**
   * SugarCRM Public License v1.1.3.
   */
  SugarCRMPublicLicenseV113: spdx_license_SugarCRMPublicLicenseV113,
  /**
   * Scheme Widget Library (SWL) Software License Agreement.
   */
  SchemeWidgetLibrarySWLSoftwareLicenseAgreement: spdx_license_SchemeWidgetLibrarySWLSoftwareLicenseAgreement,
  /**
   * TCL/TK License.
   */
  TCLTKLicense: spdx_license_TCLTKLicense,
  /**
   * TCP Wrappers License.
   */
  TCPWrappersLicense: spdx_license_TCPWrappersLicense,
  /**
   * TMate Open Source License.
   */
  TMateOpenSourceLicense: spdx_license_TMateOpenSourceLicense,
  /**
   * TORQUE v2.5+ Software License v1.1.
   */
  TORQUEV25PlusSoftwareLicenseV11: spdx_license_TORQUEV25PlusSoftwareLicenseV11,
  /**
   * Trusster Open Source License.
   */
  TrussterOpenSourceLicense: spdx_license_TrussterOpenSourceLicense,
  /**
   * Unicode License Agreement - Data Files and Software (2015).
   */
  UnicodeLicenseAgreementDataFilesAndSoftware2015: spdx_license_UnicodeLicenseAgreementDataFilesAndSoftware2015,
  /**
   * Unicode License Agreement - Data Files and Software (2016).
   */
  UnicodeLicenseAgreementDataFilesAndSoftware2016: spdx_license_UnicodeLicenseAgreementDataFilesAndSoftware2016,
  /**
   * Unicode Terms of Use.
   */
  UnicodeTermsOfUse: spdx_license_UnicodeTermsOfUse,
  /**
   * The Unlicense.
   */
  TheUnlicense: spdx_license_TheUnlicense,
  /**
   * Universal Permissive License v1.0.
   */
  UniversalPermissiveLicenseV10: spdx_license_UniversalPermissiveLicenseV10,
  /**
   * Vim License.
   */
  VimLicense: spdx_license_VimLicense,
  /**
   * VOSTROM Public License for Open Source.
   */
  VOSTROMPublicLicenseForOpenSource: spdx_license_VOSTROMPublicLicenseForOpenSource,
  /**
   * Vovida Software License v1.0.
   */
  VovidaSoftwareLicenseV10: spdx_license_VovidaSoftwareLicenseV10,
  /**
   * W3C Software Notice and License (2002-12-31).
   */
  W3CSoftwareNoticeAndLicense20021231: spdx_license_W3CSoftwareNoticeAndLicense20021231,
  /**
   * W3C Software Notice and License (1998-07-20).
   */
  W3CSoftwareNoticeAndLicense19980720: spdx_license_W3CSoftwareNoticeAndLicense19980720,
  /**
   * W3C Software Notice and Document License (2015-05-13).
   */
  W3CSoftwareNoticeAndDocumentLicense20150513: spdx_license_W3CSoftwareNoticeAndDocumentLicense20150513,
  /**
   * Sybase Open Watcom Public License 1.0.
   */
  SybaseOpenWatcomPublicLicense10: spdx_license_SybaseOpenWatcomPublicLicense10,
  /**
   * Wsuipa License.
   */
  WsuipaLicense: spdx_license_WsuipaLicense,
  /**
   * Do What The F*ck You Want To Public License.
   */
  DoWhatTheFAsteriskCkYouWantToPublicLicense: spdx_license_DoWhatTheFAsteriskCkYouWantToPublicLicense,
  /**
   * X11 License.
   */
  X11License: spdx_license_X11License,
  /**
   * Xerox License.
   */
  XeroxLicense: spdx_license_XeroxLicense,
  /**
   * XFree86 License 1.1.
   */
  XFree86License11: spdx_license_XFree86License11,
  /**
   * xinetd License.
   */
  XinetdLicense: spdx_license_XinetdLicense,
  /**
   * X.Net License.
   */
  XNetLicense: spdx_license_XNetLicense,
  /**
   * XPP License.
   */
  XPPLicense: spdx_license_XPPLicense,
  /**
   * XSkat License.
   */
  XSkatLicense: spdx_license_XSkatLicense,
  /**
   * Yahoo! Public License v1.0.
   */
  YahooNotPublicLicenseV10: spdx_license_YahooNotPublicLicenseV10,
  /**
   * Yahoo! Public License v1.1.
   */
  YahooNotPublicLicenseV11: spdx_license_YahooNotPublicLicenseV11,
  /**
   * Zed License.
   */
  ZedLicense: spdx_license_ZedLicense,
  /**
   * Zend License v2.0.
   */
  ZendLicenseV20: spdx_license_ZendLicenseV20,
  /**
   * Zimbra Public License v1.3.
   */
  ZimbraPublicLicenseV13: spdx_license_ZimbraPublicLicenseV13,
  /**
   * Zimbra Public License v1.4.
   */
  ZimbraPublicLicenseV14: spdx_license_ZimbraPublicLicenseV14,
  /**
   * zlib License.
   */
  ZlibLicense: spdx_license_ZlibLicense,
  /**
   * zlib/libpng License with Acknowledgement.
   */
  ZlibLibpngLicenseWithAcknowledgement: spdx_license_ZlibLibpngLicenseWithAcknowledgement,
  /**
   * Zope Public License 1.1.
   */
  ZopePublicLicense11: spdx_license_ZopePublicLicense11,
  /**
   * Zope Public License 2.0.
   */
  ZopePublicLicense20: spdx_license_ZopePublicLicense20,
  /**
   * Zope Public License 2.1.
   */
  ZopePublicLicense21: spdx_license_ZopePublicLicense21,
};
const special_values_False: Coding = {
  code: 'false',
  display: 'false',
  system: 'http://terminology.hl7.org/CodeSystem/special-values',
};
const special_values_NilKnown: Coding = {
  code: 'nil-known',
  display: 'Nil Known',
  system: 'http://terminology.hl7.org/CodeSystem/special-values',
};
const special_values_SufficientQuantity: Coding = {
  code: 'sufficient',
  display: 'Sufficient Quantity',
  system: 'http://terminology.hl7.org/CodeSystem/special-values',
};
const special_values_TraceAmountDetected: Coding = {
  code: 'trace',
  display: 'Trace Amount Detected',
  system: 'http://terminology.hl7.org/CodeSystem/special-values',
};
const special_values_True: Coding = {
  code: 'true',
  display: 'true',
  system: 'http://terminology.hl7.org/CodeSystem/special-values',
};
const special_values_ValueWithdrawn: Coding = {
  code: 'withdrawn',
  display: 'Value Withdrawn',
  system: 'http://terminology.hl7.org/CodeSystem/special-values',
};
/**
 * A set of generally useful codes defined so they can be included in value sets.
 */
export const SpecialValues = {
  /**
   * Boolean false.
   */
  False: special_values_False,
  /**
   * The are no known applicable values in this context.
   */
  NilKnown: special_values_NilKnown,
  /**
   * The specific quantity is not known, but is known to be non-zero and is not specified because it makes up the bulk of the material.
   */
  SufficientQuantity: special_values_SufficientQuantity,
  /**
   * The content is greater than zero, but too small to be quantified.
   */
  TraceAmountDetected: special_values_TraceAmountDetected,
  /**
   * Boolean true.
   */
  True: special_values_True,
  /**
   * The value is no longer available.
   */
  ValueWithdrawn: special_values_ValueWithdrawn,
};
const SpecimenCollection_PunctureAction: Coding = {
  code: '129300006',
  display: 'Puncture - action',
  system: 'http://snomed.info/sct',
};
const SpecimenCollection_ExcisionAction: Coding = {
  code: '129304002',
  display: 'Excision - action',
  system: 'http://snomed.info/sct',
};
const SpecimenCollection_BiopsyAction: Coding = {
  code: '129314006',
  display: 'Biopsy - action',
  system: 'http://snomed.info/sct',
};
const SpecimenCollection_AspirationAction: Coding = {
  code: '129316008',
  display: 'Aspiration - action',
  system: 'http://snomed.info/sct',
};
const SpecimenCollection_ScrapingAction: Coding = {
  code: '129323009',
  display: 'Scraping - action',
  system: 'http://snomed.info/sct',
};
const SpecimenCollection_TimedUrineCollection: Coding = {
  code: '225113003',
  display: 'Timed urine collection',
  system: 'http://snomed.info/sct',
};
const SpecimenCollection_FingerPrickSampling: Coding = {
  code: '278450005',
  display: 'Finger-prick sampling',
  system: 'http://snomed.info/sct',
};
const SpecimenCollection_CollectionOfCoughedSputum: Coding = {
  code: '386089008',
  display: 'Collection of coughed sputum',
  system: 'http://snomed.info/sct',
};
const SpecimenCollection_UrineSpecimenCollectionCatheterized: Coding = {
  code: '70777001',
  display: 'Urine specimen collection, catheterized',
  system: 'http://snomed.info/sct',
};
const SpecimenCollection_UrineSpecimenCollectionCleanCatch: Coding = {
  code: '73416001',
  display: 'Urine specimen collection, clean catch',
  system: 'http://snomed.info/sct',
};
/**
 * Actions that can be taken for the collection of specimen from a subject.
 */
export const SpecimenCollection = {
  PunctureAction: SpecimenCollection_PunctureAction,
  ExcisionAction: SpecimenCollection_ExcisionAction,
  BiopsyAction: SpecimenCollection_BiopsyAction,
  AspirationAction: SpecimenCollection_AspirationAction,
  ScrapingAction: SpecimenCollection_ScrapingAction,
  TimedUrineCollection: SpecimenCollection_TimedUrineCollection,
  FingerPrickSampling: SpecimenCollection_FingerPrickSampling,
  CollectionOfCoughedSputum: SpecimenCollection_CollectionOfCoughedSputum,
  UrineSpecimenCollectionCatheterized: SpecimenCollection_UrineSpecimenCollectionCatheterized,
  UrineSpecimenCollectionCleanCatch: SpecimenCollection_UrineSpecimenCollectionCleanCatch,
};
/**
 *  This example value set defines a set of codes that can be used to indicate the method by which a specimen was collected.
 */
export const SpecimenCollectionMethod = {
  PunctureAction: SpecimenCollection_PunctureAction,
  ExcisionAction: SpecimenCollection_ExcisionAction,
  BiopsyAction: SpecimenCollection_BiopsyAction,
  AspirationAction: SpecimenCollection_AspirationAction,
  ScrapingAction: SpecimenCollection_ScrapingAction,
  TimedUrineCollection: SpecimenCollection_TimedUrineCollection,
  FingerPrickSampling: SpecimenCollection_FingerPrickSampling,
  CollectionOfCoughedSputum: SpecimenCollection_CollectionOfCoughedSputum,
  UrineSpecimenCollectionCatheterized: SpecimenCollection_UrineSpecimenCollectionCatheterized,
  UrineSpecimenCollectionCleanCatch: SpecimenCollection_UrineSpecimenCollectionCleanCatch,
};
const SpecimenCollectionPriority_STAT: Coding = {
  code: '1',
  display: 'STAT',
  system: 'http://example.com',
};
const SpecimenCollectionPriority_ASAP: Coding = {
  code: '2',
  display: 'ASAP',
  system: 'http://example.com',
};
const SpecimenCollectionPriority_ASAPED: Coding = {
  code: '3',
  display: 'ASAP-ED',
  system: 'http://example.com',
};
const SpecimenCollectionPriority_AM: Coding = {
  code: '4',
  display: 'AM',
  system: 'http://example.com',
};
const SpecimenCollectionPriority_ROUTINE: Coding = {
  code: '5',
  display: 'ROUTINE',
  system: 'http://example.com',
};
const SpecimenCollectionPriority_NURSECOLLECT: Coding = {
  code: '6',
  display: 'NURSE COLLECT',
  system: 'http://example.com',
};
const SpecimenCollectionPriority_CALLORFAX: Coding = {
  code: '7',
  display: 'CALL OR FAX',
  system: 'http://example.com',
};
/**
 *  This example value set defines a set of codes that can be used to indicate the priority of collection of a specimen.
 */
export const SpecimenCollectionPriority = {
  STAT: SpecimenCollectionPriority_STAT,
  ASAP: SpecimenCollectionPriority_ASAP,
  ASAPED: SpecimenCollectionPriority_ASAPED,
  AM: SpecimenCollectionPriority_AM,
  ROUTINE: SpecimenCollectionPriority_ROUTINE,
  NURSECOLLECT: SpecimenCollectionPriority_NURSECOLLECT,
  CALLORFAX: SpecimenCollectionPriority_CALLORFAX,
};
const specimen_contained_preference_Alternate: Coding = {
  code: 'alternate',
  display: 'Alternate',
  system: 'http://hl7.org/fhir/specimen-contained-preference',
};
const specimen_contained_preference_Preferred: Coding = {
  code: 'preferred',
  display: 'Preferred',
  system: 'http://hl7.org/fhir/specimen-contained-preference',
};
/**
 * Degree of preference of a type of conditioned specimen.
 */
export const SpecimenContainedPreference = {
  /**
   * This type of conditioned specimen is an alternate.
   */
  Alternate: specimen_contained_preference_Alternate,
  /**
   * This type of contained specimen is preferred to collect this kind of specimen.
   */
  Preferred: specimen_contained_preference_Preferred,
};
const v2_0373_Acidification: Coding = {
  code: 'ACID',
  display: 'Acidification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0373',
};
const v2_0373_Alkalization: Coding = {
  code: 'ALK',
  display: 'Alkalization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0373',
};
const v2_0373_Defibrination: Coding = {
  code: 'DEFB',
  display: 'Defibrination',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0373',
};
const v2_0373_Filtration: Coding = {
  code: 'FILT',
  display: 'Filtration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0373',
};
const v2_0373_LDLPrecipitation: Coding = {
  code: 'LDLP',
  display: 'LDL Precipitation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0373',
};
const v2_0373_Neutralization: Coding = {
  code: 'NEUT',
  display: 'Neutralization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0373',
};
const v2_0373_Recalification: Coding = {
  code: 'RECA',
  display: 'Recalification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0373',
};
const v2_0373_Ultrafiltration: Coding = {
  code: 'UFIL',
  display: 'Ultrafiltration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0373',
};
/**
 * The technique that is used to perform the process or preserve the specimen.
 */
export const SpecimenProcessingProcedure = {
  Acidification: v2_0373_Acidification,
  Alkalization: v2_0373_Alkalization,
  Defibrination: v2_0373_Defibrination,
  Filtration: v2_0373_Filtration,
  LDLPrecipitation: v2_0373_LDLPrecipitation,
  Neutralization: v2_0373_Neutralization,
  Recalification: v2_0373_Recalification,
  Ultrafiltration: v2_0373_Ultrafiltration,
};
const specimen_status_Available: Coding = {
  code: 'available',
  display: 'Available',
  system: 'http://hl7.org/fhir/specimen-status',
};
const specimen_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/specimen-status',
};
const specimen_status_Unavailable: Coding = {
  code: 'unavailable',
  display: 'Unavailable',
  system: 'http://hl7.org/fhir/specimen-status',
};
const specimen_status_Unsatisfactory: Coding = {
  code: 'unsatisfactory',
  display: 'Unsatisfactory',
  system: 'http://hl7.org/fhir/specimen-status',
};
/**
 * Codes providing the status/availability of a specimen.
 */
export const SpecimenStatus = {
  /**
   * The physical specimen is present and in good condition.
   */
  Available: specimen_status_Available,
  /**
   * The specimen was entered in error and therefore nullified.
   */
  EnteredInError: specimen_status_EnteredInError,
  /**
   * There is no physical specimen because it is either lost, destroyed or consumed.
   */
  Unavailable: specimen_status_Unavailable,
  /**
   * The specimen cannot be used because of a quality issue such as a broken container, contamination, or too old.
   */
  Unsatisfactory: specimen_status_Unsatisfactory,
};
const standards_status_Deprecated: Coding = {
  code: 'deprecated',
  display: 'Deprecated',
  system: 'http://terminology.hl7.org/CodeSystem/standards-status',
};
const standards_status_Draft: Coding = {
  code: 'draft',
  display: 'Draft',
  system: 'http://terminology.hl7.org/CodeSystem/standards-status',
};
const standards_status_External: Coding = {
  code: 'external',
  display: 'External',
  system: 'http://terminology.hl7.org/CodeSystem/standards-status',
};
const standards_status_Informative: Coding = {
  code: 'informative',
  display: 'Informative',
  system: 'http://terminology.hl7.org/CodeSystem/standards-status',
};
const standards_status_Normative: Coding = {
  code: 'normative',
  display: 'Normative',
  system: 'http://terminology.hl7.org/CodeSystem/standards-status',
};
const standards_status_TrialUse: Coding = {
  code: 'trial-use',
  display: 'Trial-Use',
  system: 'http://terminology.hl7.org/CodeSystem/standards-status',
};
/**
 * HL7 Ballot/Standards status of artifact.
 */
export const StandardsStatus = {
  /**
   * This portion of the specification is provided for implementer assistance, and does not make rules that implementers are required to follow. Typical examples of this content in the FHIR specification are tables of contents, registries, examples, and implementer advice.
   */
  Deprecated: standards_status_Deprecated,
  /**
   * This portion of the specification is not considered to be complete enough or sufficiently reviewed to be safe for implementation. It may have known issues or still be in the "in development" stage. It is included in the publication as a place-holder, to solicit feedback from the implementation community and/or to give implementers some insight as to functionality likely to be included in future versions of the specification. Content at this level should only be implemented by the brave or desperate and is very much "use at your own risk". The content that is Draft that will usually be elevated to Trial Use once review and correction is complete after it has been subjected to ballot.
   */
  Draft: standards_status_Draft,
  /**
   * This is content that is managed outside the FHIR Specification, but included for implementer convenience.
   */
  External: standards_status_External,
  /**
   * This portion of the specification is provided for implementer assistance, and does not make rules that implementers are required to follow. Typical examples of this content in the FHIR specification are tables of contents, registries, examples, and implementer advice.
   */
  Informative: standards_status_Informative,
  /**
   * This content has been subject to review and production implementation in a wide variety of environments. The content is considered to be stable and has been 'locked', subjecting it to FHIR Inter-version Compatibility Rules. While changes are possible, they are expected to be infrequent and are tightly constrained. Compatibility Rules.
   */
  Normative: standards_status_Normative,
  /**
   * This content has been well reviewed and is considered by the authors to be ready for use in production systems. It has been subjected to ballot and approved as an official standard. However, it has not yet seen widespread use in production across the full spectrum of environments it is intended to be used in. In some cases, there may be documented known issues that require implementation experience to determine appropriate resolutions for.
   * Future versions of FHIR may make significant changes to Trial Use content that are not compatible with previously published content.
   */
  TrialUse: standards_status_TrialUse,
};
const strand_type_CrickStrandOfReferenceSeq: Coding = {
  code: 'crick',
  display: 'Crick strand of referenceSeq',
  system: 'http://hl7.org/fhir/strand-type',
};
const strand_type_WatsonStrandOfReferenceSeq: Coding = {
  code: 'watson',
  display: 'Watson strand of referenceSeq',
  system: 'http://hl7.org/fhir/strand-type',
};
/**
 * Type for strand.
 */
export const StrandType = {
  /**
   * Crick strand of reference sequence.
   */
  CrickStrandOfReferenceSeq: strand_type_CrickStrandOfReferenceSeq,
  /**
   * Watson strand of reference sequence.
   */
  WatsonStrandOfReferenceSeq: strand_type_WatsonStrandOfReferenceSeq,
};
const structure_definition_kind_ComplexDataType: Coding = {
  code: 'complex-type',
  display: 'Complex Data Type',
  system: 'http://hl7.org/fhir/structure-definition-kind',
};
const structure_definition_kind_Logical: Coding = {
  code: 'logical',
  display: 'Logical',
  system: 'http://hl7.org/fhir/structure-definition-kind',
};
const structure_definition_kind_PrimitiveDataType: Coding = {
  code: 'primitive-type',
  display: 'Primitive Data Type',
  system: 'http://hl7.org/fhir/structure-definition-kind',
};
const structure_definition_kind_Resource: Coding = {
  code: 'resource',
  display: 'Resource',
  system: 'http://hl7.org/fhir/structure-definition-kind',
};
/**
 * Defines the type of structure that a definition is describing.
 */
export const StructureDefinitionKind = {
  /**
   * A  complex structure that defines a set of data elements that is suitable for use in 'resources'. The base specification defines a number of complex types, and other specifications can define additional types. These structures do not have a maintained identity.
   */
  ComplexDataType: structure_definition_kind_ComplexDataType,
  /**
   * A pattern or a template that is not intended to be a real resource or complex type.
   */
  Logical: structure_definition_kind_Logical,
  /**
   * A primitive type that has a value and an extension. These can be used throughout complex datatype, Resource and extension definitions. Only the base specification can define primitive types.
   */
  PrimitiveDataType: structure_definition_kind_PrimitiveDataType,
  /**
   * A 'resource' - a directed acyclic graph of elements that aggregrates other types into an identifiable entity. The base FHIR resources are defined by the FHIR specification itself but other 'resources' can be defined in additional specifications (though these will not be recognised as 'resources' by the FHIR specification (i.e. they do not get end-points etc, or act as the targets of references in FHIR defined resources - though other specificatiosn can treat them this way).
   */
  Resource: structure_definition_kind_Resource,
};
const study_type_CaseControlStudy: Coding = {
  code: 'case-control',
  display: 'case-control study',
  system: 'http://terminology.hl7.org/CodeSystem/study-type',
};
const study_type_CaseReport: Coding = {
  code: 'case-report',
  display: 'case report',
  system: 'http://terminology.hl7.org/CodeSystem/study-type',
};
const study_type_ControlledTrialNonRandomized: Coding = {
  code: 'CCT',
  display: 'controlled trial (non-randomized)',
  system: 'http://terminology.hl7.org/CodeSystem/study-type',
};
const study_type_ComparativeCohortStudy: Coding = {
  code: 'cohort',
  display: 'comparative cohort study',
  system: 'http://terminology.hl7.org/CodeSystem/study-type',
};
const study_type_MixedMethods: Coding = {
  code: 'mixed',
  display: 'mixed methods',
  system: 'http://terminology.hl7.org/CodeSystem/study-type',
};
const study_type_RandomizedTrial: Coding = {
  code: 'RCT',
  display: 'randomized trial',
  system: 'http://terminology.hl7.org/CodeSystem/study-type',
};
const study_type_UncontrolledCohortOrCaseSeries: Coding = {
  code: 'series',
  display: 'uncontrolled cohort or case series',
  system: 'http://terminology.hl7.org/CodeSystem/study-type',
};
/**
 * Types of research studies (types of research methods).
 */
export const StudyType = {
  /**
   * case-control study.
   */
  CaseControlStudy: study_type_CaseControlStudy,
  /**
   * a single case report.
   */
  CaseReport: study_type_CaseReport,
  /**
   * controlled (but not randomized) trial.
   */
  ControlledTrialNonRandomized: study_type_ControlledTrialNonRandomized,
  /**
   * observational study comparing cohorts.
   */
  ComparativeCohortStudy: study_type_ComparativeCohortStudy,
  /**
   * a combination of 1 or more types of studies.
   */
  MixedMethods: study_type_MixedMethods,
  /**
   * randomized controlled trial.
   */
  RandomizedTrial: study_type_RandomizedTrial,
  /**
   * uncontrolled cohort or case series.
   */
  UncontrolledCohortOrCaseSeries: study_type_UncontrolledCohortOrCaseSeries,
};
/**
 * Possible types of subjects.
 */
export const SubjectType = {
  /**
   * A type of a manufactured item that is used in the provision of healthcare without being substantially changed through that activity. The device may be a medical or non-medical device.
   */
  Device: resource_types_Device,
  /**
   * Details and position information for a physical place where services are provided and resources and participants may be stored, found, contained, or accommodated.
   */
  Location: resource_types_Location,
  /**
   * A formally or informally recognized grouping of people or organizations formed for the purpose of achieving some form of collective action.  Includes companies, institutions, corporations, departments, community groups, healthcare practice groups, payer/insurer, etc.
   */
  Organization: resource_types_Organization,
  /**
   * Demographics and other administrative information about an individual or animal receiving care or other health-related services.
   */
  Patient: resource_types_Patient,
  /**
   * A person who is directly or indirectly involved in the provisioning of healthcare.
   */
  Practitioner: resource_types_Practitioner,
};
const subscriber_relationship_Child: Coding = {
  code: 'child',
  display: 'Child',
  system: 'http://terminology.hl7.org/CodeSystem/subscriber-relationship',
};
const subscriber_relationship_CommonLawSpouse: Coding = {
  code: 'common',
  display: 'Common Law Spouse',
  system: 'http://terminology.hl7.org/CodeSystem/subscriber-relationship',
};
const subscriber_relationship_InjuredParty: Coding = {
  code: 'injured',
  display: 'Injured Party',
  system: 'http://terminology.hl7.org/CodeSystem/subscriber-relationship',
};
const subscriber_relationship_Other: Coding = {
  code: 'other',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/subscriber-relationship',
};
const subscriber_relationship_Parent: Coding = {
  code: 'parent',
  display: 'Parent',
  system: 'http://terminology.hl7.org/CodeSystem/subscriber-relationship',
};
const subscriber_relationship_Self: Coding = {
  code: 'self',
  display: 'Self',
  system: 'http://terminology.hl7.org/CodeSystem/subscriber-relationship',
};
const subscriber_relationship_Spouse: Coding = {
  code: 'spouse',
  display: 'Spouse',
  system: 'http://terminology.hl7.org/CodeSystem/subscriber-relationship',
};
/**
 * This value set includes codes for the relationship between the Subscriber and the Beneficiary (insured/covered party/patient).
 */
export const SubscriberRelationship = {
  /**
   * The Beneficiary is a child of the Subscriber
   */
  Child: subscriber_relationship_Child,
  /**
   * The Beneficiary is a common law spouse or equivalent of the Subscriber
   */
  CommonLawSpouse: subscriber_relationship_CommonLawSpouse,
  /**
   * The Beneficiary is covered under insurance of the subscriber due to an injury.
   */
  InjuredParty: subscriber_relationship_InjuredParty,
  /**
   * The Beneficiary has some other relationship the Subscriber
   */
  Other: subscriber_relationship_Other,
  /**
   * The Beneficiary is a parent of the Subscriber
   */
  Parent: subscriber_relationship_Parent,
  /**
   * The Beneficiary is the Subscriber
   */
  Self: subscriber_relationship_Self,
  /**
   * The Beneficiary is a spouse or equivalent of the Subscriber
   */
  Spouse: subscriber_relationship_Spouse,
};
const subscription_channel_type_Email: Coding = {
  code: 'email',
  display: 'Email',
  system: 'http://hl7.org/fhir/subscription-channel-type',
};
const subscription_channel_type_Message: Coding = {
  code: 'message',
  display: 'Message',
  system: 'http://hl7.org/fhir/subscription-channel-type',
};
const subscription_channel_type_RestHook: Coding = {
  code: 'rest-hook',
  display: 'Rest Hook',
  system: 'http://hl7.org/fhir/subscription-channel-type',
};
const subscription_channel_type_SMS: Coding = {
  code: 'sms',
  display: 'SMS',
  system: 'http://hl7.org/fhir/subscription-channel-type',
};
const subscription_channel_type_Websocket: Coding = {
  code: 'websocket',
  display: 'Websocket',
  system: 'http://hl7.org/fhir/subscription-channel-type',
};
/**
 * The type of method used to execute a subscription.
 */
export const SubscriptionChannelType = {
  /**
   * The channel is executed by sending an email to the email addressed in the URI (which must be a mailto:).
   */
  Email: subscription_channel_type_Email,
  /**
   * The channel is executed by sending a message (e.g. a Bundle with a MessageHeader resource etc.) to the application identified in the URI.
   */
  Message: subscription_channel_type_Message,
  /**
   * The channel is executed by making a post to the URI. If a payload is included, the URL is interpreted as the service base, and an update (PUT) is made.
   */
  RestHook: subscription_channel_type_RestHook,
  /**
   * The channel is executed by sending an SMS message to the phone number identified in the URL (tel:).
   */
  SMS: subscription_channel_type_SMS,
  /**
   * The channel is executed by sending a packet across a web socket connection maintained by the client. The URL identifies the websocket, and the client binds to this URL.
   */
  Websocket: subscription_channel_type_Websocket,
};
const subscription_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/subscription-status',
};
const subscription_status_Error: Coding = {
  code: 'error',
  display: 'Error',
  system: 'http://hl7.org/fhir/subscription-status',
};
const subscription_status_Off: Coding = {
  code: 'off',
  display: 'Off',
  system: 'http://hl7.org/fhir/subscription-status',
};
const subscription_status_Requested: Coding = {
  code: 'requested',
  display: 'Requested',
  system: 'http://hl7.org/fhir/subscription-status',
};
/**
 * The status of a subscription.
 */
export const SubscriptionStatus = {
  /**
   * The subscription is active.
   */
  Active: subscription_status_Active,
  /**
   * The server has an error executing the notification.
   */
  Error: subscription_status_Error,
  /**
   * Too many errors have occurred or the subscription has expired.
   */
  Off: subscription_status_Off,
  /**
   * The client has requested the subscription, and the server has not yet set it up.
   */
  Requested: subscription_status_Requested,
};
const subscription_tag_Delivered: Coding = {
  code: 'delivered',
  display: 'Delivered',
  system: 'http://terminology.hl7.org/CodeSystem/subscription-tag',
};
const subscription_tag_Queued: Coding = {
  code: 'queued',
  display: 'Queued',
  system: 'http://terminology.hl7.org/CodeSystem/subscription-tag',
};
/**
 * Tags to put on a resource after subscriptions have been sent.
 */
export const SubscriptionTag = {
  /**
   * The message has been delivered to its intended recipient.
   */
  Delivered: subscription_tag_Delivered,
  /**
   * The message has been queued for processing on a destination systems.
   */
  Queued: subscription_tag_Queued,
};
const substance_category_Allergen: Coding = {
  code: 'allergen',
  display: 'Allergen',
  system: 'http://terminology.hl7.org/CodeSystem/substance-category',
};
const substance_category_BiologicalSubstance: Coding = {
  code: 'biological',
  display: 'Biological Substance',
  system: 'http://terminology.hl7.org/CodeSystem/substance-category',
};
const substance_category_BodySubstance: Coding = {
  code: 'body',
  display: 'Body Substance',
  system: 'http://terminology.hl7.org/CodeSystem/substance-category',
};
const substance_category_Chemical: Coding = {
  code: 'chemical',
  display: 'Chemical',
  system: 'http://terminology.hl7.org/CodeSystem/substance-category',
};
const substance_category_DrugOrMedicament: Coding = {
  code: 'drug',
  display: 'Drug or Medicament',
  system: 'http://terminology.hl7.org/CodeSystem/substance-category',
};
const substance_category_DietarySubstance: Coding = {
  code: 'food',
  display: 'Dietary Substance',
  system: 'http://terminology.hl7.org/CodeSystem/substance-category',
};
const substance_category_Material: Coding = {
  code: 'material',
  display: 'Material',
  system: 'http://terminology.hl7.org/CodeSystem/substance-category',
};
/**
 * Substance category codes
 */
export const SubstanceCategory = {
  /**
   * A substance that causes an allergic reaction.
   */
  Allergen: substance_category_Allergen,
  /**
   * A substance that is produced by or extracted from a biological source.
   */
  BiologicalSubstance: substance_category_BiologicalSubstance,
  /**
   * A substance that comes directly from a human or an animal (e.g. blood, urine, feces, tears, etc.).
   */
  BodySubstance: substance_category_BodySubstance,
  /**
   * Any organic or inorganic substance of a particular molecular identity, including -- (i) any combination of such substances occurring in whole or in part as a result of a chemical reaction or occurring in nature and (ii) any element or uncombined radical (http://www.epa.gov/opptintr/import-export/pubs/importguide.pdf).
   */
  Chemical: substance_category_Chemical,
  /**
   * A substance intended for use in the diagnosis, cure, mitigation, treatment, or prevention of disease in man or other animals (Federal Food Drug and Cosmetic Act).
   */
  DrugOrMedicament: substance_category_DrugOrMedicament,
  /**
   * A food, dietary ingredient, or dietary supplement for human or animal.
   */
  DietarySubstance: substance_category_DietarySubstance,
  /**
   * A finished product which is not normally ingested, absorbed or injected (e.g. steel, iron, wood, plastic and paper).
   */
  Material: substance_category_Material,
};
const substance_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/substance-status',
};
const substance_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/substance-status',
};
const substance_status_Inactive: Coding = {
  code: 'inactive',
  display: 'Inactive',
  system: 'http://hl7.org/fhir/substance-status',
};
/**
 * A code to indicate if the substance is actively used.
 */
export const SubstanceStatus = {
  /**
   * The substance is considered for use or reference.
   */
  Active: substance_status_Active,
  /**
   * The substance was entered in error.
   */
  EnteredInError: substance_status_EnteredInError,
  /**
   * The substance is considered for reference, but not for use.
   */
  Inactive: substance_status_Inactive,
};
const SupplementType_AdultClearLiquidSupplement: Coding = {
  code: '442901000124106',
  display: 'Adult clear liquid supplement',
  system: 'http://snomed.info/sct',
};
const SupplementType_PediatricClearLiquidSupplement: Coding = {
  code: '443391000124108',
  display: 'Pediatric clear liquid supplement',
  system: 'http://snomed.info/sct',
};
const SupplementType_ClearLiquidSupplement: Coding = {
  code: '443441000124107',
  display: 'Clear liquid supplement',
  system: 'http://snomed.info/sct',
};
const SupplementType_StandardPuddingOralSupplement: Coding = {
  code: '444321000124108',
  display: 'Standard pudding oral supplement',
  system: 'http://snomed.info/sct',
};
const SupplementType_DiabeticMealReplacementBar: Coding = {
  code: '444331000124106',
  display: 'Diabetic meal replacement bar',
  system: 'http://snomed.info/sct',
};
const SupplementType_ElectrolyteReplacementSupplement: Coding = {
  code: '444361000124102',
  display: 'Electrolyte replacement supplement',
  system: 'http://snomed.info/sct',
};
const SupplementType_PowderedElectrolyteReplacementSupplement: Coding = {
  code: '444371000124109',
  display: 'Powdered electrolyte replacement supplement',
  system: 'http://snomed.info/sct',
};
const SupplementType_LiquidElectrolyteReplacementSupplement: Coding = {
  code: '444381000124107',
  display: 'Liquid electrolyte replacement supplement',
  system: 'http://snomed.info/sct',
};
const SupplementType_FrozenElectrolyteReplacementSupplement: Coding = {
  code: '444401000124107',
  display: 'Frozen electrolyte replacement supplement',
  system: 'http://snomed.info/sct',
};
/**
 * SupplementType :  Codes for nutritional supplements to be provided to the patient. This value set is composed of SNOMED CT (US Extension) Concepts from SCTID 470581016 (Enteral+supplement feeds hierarchy (product)) and is provided as a suggestive example.
 */
export const SupplementType = {
  StandardEnteralFormula: EntformulaType_StandardEnteralFormula,
  StandardEnteralFormulaWithFiber: EntformulaType_StandardEnteralFormulaWithFiber,
  HighEnergyEnteralFormulaWithFiber: EntformulaType_HighEnergyEnteralFormulaWithFiber,
  DiabeticEnteralFormulaWithFiber: EntformulaType_DiabeticEnteralFormulaWithFiber,
  DiabeticHighCalorieHighProteinEnteralFormulaWithFiber: EntformulaType_DiabeticHighCalorieHighProteinEnteralFormulaWithFiber,
  HydrolyzedPeptideBasedHighProteinEnteralFormula: EntformulaType_HydrolyzedPeptideBasedHighProteinEnteralFormula,
  AdultFormula: EntformulaType_AdultFormula,
  AdultClearLiquidSupplement: SupplementType_AdultClearLiquidSupplement,
  AdultElementalFormula: EntformulaType_AdultElementalFormula,
  AdultStandardFormula: EntformulaType_AdultStandardFormula,
  AdultSoyProteinIsolateFormula: EntformulaType_AdultSoyProteinIsolateFormula,
  AdultRenalSpecialtyFormula: EntformulaType_AdultRenalSpecialtyFormula,
  AdultPulmonarySpecialtyFormula: EntformulaType_AdultPulmonarySpecialtyFormula,
  AdultLowCarbohydrateFormula: EntformulaType_AdultLowCarbohydrateFormula,
  AdultHighEnergyFormula: EntformulaType_AdultHighEnergyFormula,
  AdultHydrolyzedProteinFormula: EntformulaType_AdultHydrolyzedProteinFormula,
  AdultHighProteinFormula: EntformulaType_AdultHighProteinFormula,
  AdultHighProteinHighFiberFormula: EntformulaType_AdultHighProteinHighFiberFormula,
  AdultHepaticSpecialtyFormula: EntformulaType_AdultHepaticSpecialtyFormula,
  AdultCriticalCareFormula: EntformulaType_AdultCriticalCareFormula,
  AdultDiabetesSpecialtyFormula: EntformulaType_AdultDiabetesSpecialtyFormula,
  HighProteinFormula: EntformulaType_HighProteinFormula,
  IncreasedFiberFormula: EntformulaType_IncreasedFiberFormula,
  PediatricFormula: EntformulaType_PediatricFormula,
  PediatricClearLiquidSupplement: SupplementType_PediatricClearLiquidSupplement,
  PediatricElementalFormula: EntformulaType_PediatricElementalFormula,
  ElementalFormula: EntformulaType_ElementalFormula,
  PediatricHydrolyzedProteinFormula: EntformulaType_PediatricHydrolyzedProteinFormula,
  HighEnergyFormula: EntformulaType_HighEnergyFormula,
  ClearLiquidSupplement: SupplementType_ClearLiquidSupplement,
  PediatricStandardFormula: EntformulaType_PediatricStandardFormula,
  StandardFormula: EntformulaType_StandardFormula,
  PediatricIncreasedFiberFormula: EntformulaType_PediatricIncreasedFiberFormula,
  RenalFormula: EntformulaType_RenalFormula,
  PediatricHighEnergyFormula: EntformulaType_PediatricHighEnergyFormula,
  PediatricHighEnergyFormulaWithIncreasedFiber: EntformulaType_PediatricHighEnergyFormulaWithIncreasedFiber,
  SoyBasedFormula: EntformulaType_SoyBasedFormula,
  HydrolyzedProteinFormula: EntformulaType_HydrolyzedProteinFormula,
  StandardPuddingOralSupplement: SupplementType_StandardPuddingOralSupplement,
  DiabeticMealReplacementBar: SupplementType_DiabeticMealReplacementBar,
  ElectrolyteReplacementSupplement: SupplementType_ElectrolyteReplacementSupplement,
  PowderedElectrolyteReplacementSupplement: SupplementType_PowderedElectrolyteReplacementSupplement,
  LiquidElectrolyteReplacementSupplement: SupplementType_LiquidElectrolyteReplacementSupplement,
  FrozenElectrolyteReplacementSupplement: SupplementType_FrozenElectrolyteReplacementSupplement,
  PediatricReducedEnergyFormula: EntformulaType_PediatricReducedEnergyFormula,
};
const supplydelivery_status_Abandoned: Coding = {
  code: 'abandoned',
  display: 'Abandoned',
  system: 'http://hl7.org/fhir/supplydelivery-status',
};
const supplydelivery_status_Delivered: Coding = {
  code: 'completed',
  display: 'Delivered',
  system: 'http://hl7.org/fhir/supplydelivery-status',
};
const supplydelivery_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered In Error',
  system: 'http://hl7.org/fhir/supplydelivery-status',
};
const supplydelivery_status_InProgress: Coding = {
  code: 'in-progress',
  display: 'In Progress',
  system: 'http://hl7.org/fhir/supplydelivery-status',
};
/**
 * Status of the supply delivery.
 */
export const SupplydeliveryStatus = {
  /**
   * Delivery was not completed.
   */
  Abandoned: supplydelivery_status_Abandoned,
  /**
   * Supply has been delivered ("completed").
   */
  Delivered: supplydelivery_status_Delivered,
  /**
   * This electronic record should never have existed, though it is possible that real-world decisions were based on it. (If real-world activity has occurred, the status should be "abandoned" rather than "entered-in-error".).
   */
  EnteredInError: supplydelivery_status_EnteredInError,
  /**
   * Supply has been requested, but not delivered.
   */
  InProgress: supplydelivery_status_InProgress,
};
const supplydelivery_type_Device: Coding = {
  code: 'device',
  display: 'Device',
  system: 'http://terminology.hl7.org/CodeSystem/supply-item-type',
};
const supplydelivery_type_Medication: Coding = {
  code: 'medication',
  display: 'Medication',
  system: 'http://terminology.hl7.org/CodeSystem/supply-item-type',
};
/**
 * This value sets refers to a specific supply item.
 */
export const SupplydeliveryType = {
  /**
   * What is supplied (or requested) is a device.
   */
  Device: supplydelivery_type_Device,
  /**
   * Supply is a kind of medication.
   */
  Medication: supplydelivery_type_Medication,
};
const supplyrequest_kind_CentralSupply: Coding = {
  code: 'central',
  display: 'Central Supply',
  system: 'http://terminology.hl7.org/CodeSystem/supply-kind',
};
const supplyrequest_kind_NonStock: Coding = {
  code: 'nonstock',
  display: 'Non-Stock',
  system: 'http://terminology.hl7.org/CodeSystem/supply-kind',
};
/**
 * This value sets refers to a Category of supply.
 */
export const SupplyrequestKind = {
  /**
   * Supply is stored and requested from central supply.
   */
  CentralSupply: supplyrequest_kind_CentralSupply,
  /**
   * Supply is not onsite and must be requested from an outside vendor using a non-stock requisition.
   */
  NonStock: supplyrequest_kind_NonStock,
};
const supplyrequest_reason_PatientCare: Coding = {
  code: 'patient-care',
  display: 'Patient Care',
  system: 'http://terminology.hl7.org/CodeSystem/supplyrequest-reason',
};
const supplyrequest_reason_WardStock: Coding = {
  code: 'ward-stock',
  display: 'Ward Stock',
  system: 'http://terminology.hl7.org/CodeSystem/supplyrequest-reason',
};
/**
 * The reason why the supply item was requested.
 */
export const SupplyrequestReason = {
  /**
   * The supply has been requested for use in direct patient care.
   */
  PatientCare: supplyrequest_reason_PatientCare,
  /**
   * The supply has been requested for creating or replenishing ward stock.
   */
  WardStock: supplyrequest_reason_WardStock,
};
const supplyrequest_status_Active: Coding = {
  code: 'active',
  display: 'Active',
  system: 'http://hl7.org/fhir/supplyrequest-status',
};
const supplyrequest_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/supplyrequest-status',
};
const supplyrequest_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/supplyrequest-status',
};
const supplyrequest_status_Draft: Coding = {
  code: 'draft',
  display: 'Draft',
  system: 'http://hl7.org/fhir/supplyrequest-status',
};
const supplyrequest_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/supplyrequest-status',
};
const supplyrequest_status_Suspended: Coding = {
  code: 'suspended',
  display: 'Suspended',
  system: 'http://hl7.org/fhir/supplyrequest-status',
};
const supplyrequest_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/supplyrequest-status',
};
/**
 * Status of the supply request.
 */
export const SupplyrequestStatus = {
  /**
   * The request is ready to be acted upon.
   */
  Active: supplyrequest_status_Active,
  /**
   * The authorization/request to act has been terminated prior to the full completion of the intended actions.  No further activity should occur.
   */
  Cancelled: supplyrequest_status_Cancelled,
  /**
   * Activity against the request has been sufficiently completed to the satisfaction of the requester.
   */
  Completed: supplyrequest_status_Completed,
  /**
   * The request has been created but is not yet complete or ready for action.
   */
  Draft: supplyrequest_status_Draft,
  /**
   * This electronic record should never have existed, though it is possible that real-world decisions were based on it.  (If real-world activity has occurred, the status should be "cancelled" rather than "entered-in-error".).
   */
  EnteredInError: supplyrequest_status_EnteredInError,
  /**
   * The authorization/request to act has been temporarily withdrawn but is expected to resume in the future.
   */
  Suspended: supplyrequest_status_Suspended,
  /**
   * The authoring/source system does not know which of the status values currently applies for this observation. Note: This concept is not to be used for "other" - one of the listed statuses is presumed to apply, but the authoring/source system does not know which.
   */
  Unknown: supplyrequest_status_Unknown,
};
const surface_Buccal: Coding = {
  code: 'B',
  display: 'Buccal',
  system: 'http://terminology.hl7.org/CodeSystem/FDI-surface',
};
const surface_Distal: Coding = {
  code: 'D',
  display: 'Distal',
  system: 'http://terminology.hl7.org/CodeSystem/FDI-surface',
};
const surface_Distoincisal: Coding = {
  code: 'DI',
  display: 'Distoincisal',
  system: 'http://terminology.hl7.org/CodeSystem/FDI-surface',
};
const surface_Distoclusal: Coding = {
  code: 'DO',
  display: 'Distoclusal',
  system: 'http://terminology.hl7.org/CodeSystem/FDI-surface',
};
const surface_Incisal: Coding = {
  code: 'I',
  display: 'Incisal',
  system: 'http://terminology.hl7.org/CodeSystem/FDI-surface',
};
const surface_Lingual: Coding = {
  code: 'L',
  display: 'Lingual',
  system: 'http://terminology.hl7.org/CodeSystem/FDI-surface',
};
const surface_Mesial: Coding = {
  code: 'M',
  display: 'Mesial',
  system: 'http://terminology.hl7.org/CodeSystem/FDI-surface',
};
const surface_Mesioclusal: Coding = {
  code: 'MO',
  display: 'Mesioclusal',
  system: 'http://terminology.hl7.org/CodeSystem/FDI-surface',
};
const surface_Mesioclusodistal: Coding = {
  code: 'MOD',
  display: 'Mesioclusodistal',
  system: 'http://terminology.hl7.org/CodeSystem/FDI-surface',
};
const surface_Occlusal: Coding = {
  code: 'O',
  display: 'Occlusal',
  system: 'http://terminology.hl7.org/CodeSystem/FDI-surface',
};
const surface_Ventral: Coding = {
  code: 'V',
  display: 'Ventral',
  system: 'http://terminology.hl7.org/CodeSystem/FDI-surface',
};
/**
 * This value set includes a smattering of FDI tooth surface codes.
 */
export const Surface = {
  /**
   * The surface of a posterior tooth facing the cheeks.
   */
  Buccal: surface_Buccal,
  /**
   * The surface of a tooth that faces away from the midline of the face.
   */
  Distal: surface_Distal,
  /**
   * The Distoincisal surfaces of a tooth.
   */
  Distoincisal: surface_Distoincisal,
  /**
   * The Distoclusal surfaces of a tooth.
   */
  Distoclusal: surface_Distoclusal,
  /**
   * The biting edge of anterior teeth.
   */
  Incisal: surface_Incisal,
  /**
   * The surface of a tooth facing the tongue.
   */
  Lingual: surface_Lingual,
  /**
   * The surface of a tooth that is closest to the midline (middle) of the face.
   */
  Mesial: surface_Mesial,
  /**
   * The Mesioclusal surfaces of a tooth.
   */
  Mesioclusal: surface_Mesioclusal,
  /**
   * The Mesioclusodistal surfaces of a tooth.
   */
  Mesioclusodistal: surface_Mesioclusodistal,
  /**
   * The chewing surface of posterior teeth.
   */
  Occlusal: surface_Occlusal,
  /**
   * The surface of a tooth facing the lips.
   */
  Ventral: surface_Ventral,
};
const synthesis_type_ClassifcationOfResults: Coding = {
  code: 'classification',
  display: 'classifcation of results',
  system: 'http://terminology.hl7.org/CodeSystem/synthesis-type',
};
const synthesis_type_CombinedDirectPlusIndirectNetworkMetaAnalysis: Coding = {
  code: 'combined-NMA',
  display: 'combined direct plus indirect network meta-analysis',
  system: 'http://terminology.hl7.org/CodeSystem/synthesis-type',
};
const synthesis_type_IndirectNetworkMetaAnalysis: Coding = {
  code: 'indirect-NMA',
  display: 'indirect network meta-analysis',
  system: 'http://terminology.hl7.org/CodeSystem/synthesis-type',
};
const synthesis_type_IndividualPatientDataMetaAnalysis: Coding = {
  code: 'IPD-MA',
  display: 'individual patient data meta-analysis',
  system: 'http://terminology.hl7.org/CodeSystem/synthesis-type',
};
const synthesis_type_RangeOfResults: Coding = {
  code: 'range',
  display: 'range of results',
  system: 'http://terminology.hl7.org/CodeSystem/synthesis-type',
};
const synthesis_type_SummaryDataMetaAnalysis: Coding = {
  code: 'std-MA',
  display: 'summary data meta-analysis',
  system: 'http://terminology.hl7.org/CodeSystem/synthesis-type',
};
/**
 * Types of combining results from a body of evidence (eg. summary data meta-analysis).
 */
export const SynthesisType = {
  /**
   * An approach describing a body of evidence by categorically classifying individual studies (eg 3 studies showed beneft and 2 studied found no effect).
   */
  ClassifcationOfResults: synthesis_type_ClassifcationOfResults,
  /**
   * An composite meta-analysis derived from direct comparisons and indirect comparisons in a network meta-analysis.
   */
  CombinedDirectPlusIndirectNetworkMetaAnalysis: synthesis_type_CombinedDirectPlusIndirectNetworkMetaAnalysis,
  /**
   * An indirect meta-analysis derived from 2 or more direct comparisons in a network meta-analysis.
   */
  IndirectNetworkMetaAnalysis: synthesis_type_IndirectNetworkMetaAnalysis,
  /**
   * A meta-analysis of the individual participant data from individual studies or data sets.
   */
  IndividualPatientDataMetaAnalysis: synthesis_type_IndividualPatientDataMetaAnalysis,
  /**
   * A range of results across a body of evidence.
   */
  RangeOfResults: synthesis_type_RangeOfResults,
  /**
   * A meta-analysis of the summary data of estimates from individual studies or data sets.
   */
  SummaryDataMetaAnalysis: synthesis_type_SummaryDataMetaAnalysis,
};
/**
 * Operations supported by REST at the system level.
 */
export const SystemRestfulInteraction = {
  /**
   * perform a set of a separate interactions in a single http operation
   */
  Batch: restful_interaction_Batch,
  /**
   * Retrieve the change history for all resources on a system.
   */
  HistorySystem: restful_interaction_HistorySystem,
  /**
   * Search all resources based on some filter criteria.
   */
  SearchSystem: restful_interaction_SearchSystem,
  /**
   * Update, create or delete a set of resources as a single transaction.
   */
  Transaction: restful_interaction_Transaction,
};
const task_code_MarkTheFocalResourceAsNoLongerActive: Coding = {
  code: 'abort',
  display: 'Mark the focal resource as no longer active',
  system: 'http://hl7.org/fhir/CodeSystem/task-code',
};
const task_code_ActivateApproveTheFocalResource: Coding = {
  code: 'approve',
  display: 'Activate/approve the focal resource',
  system: 'http://hl7.org/fhir/CodeSystem/task-code',
};
const task_code_ChangeTheFocalResource: Coding = {
  code: 'change',
  display: 'Change the focal resource',
  system: 'http://hl7.org/fhir/CodeSystem/task-code',
};
const task_code_FulfillTheFocalRequest: Coding = {
  code: 'fulfill',
  display: 'Fulfill the focal request',
  system: 'http://hl7.org/fhir/CodeSystem/task-code',
};
const task_code_ReplaceTheFocalResourceWithTheInputResource: Coding = {
  code: 'replace',
  display: 'Replace the focal resource with the input resource',
  system: 'http://hl7.org/fhir/CodeSystem/task-code',
};
const task_code_ReActivateTheFocalResource: Coding = {
  code: 'resume',
  display: 'Re-activate the focal resource',
  system: 'http://hl7.org/fhir/CodeSystem/task-code',
};
const task_code_SuspendTheFocalResource: Coding = {
  code: 'suspend',
  display: 'Suspend the focal resource',
  system: 'http://hl7.org/fhir/CodeSystem/task-code',
};
/**
 * Codes indicating the type of action that is expected to be performed
 */
export const TaskCode = {
  /**
   * Abort, cancel or withdraw the focal resource, as appropriate for the type of resource.
   */
  MarkTheFocalResourceAsNoLongerActive: task_code_MarkTheFocalResourceAsNoLongerActive,
  /**
   * Take what actions are needed to transition the focus resource from 'draft' to 'active' or 'in-progress', as appropriate for the resource type.  This may involve additing additional content, approval, validation, etc.
   */
  ActivateApproveTheFocalResource: task_code_ActivateApproveTheFocalResource,
  /**
   * Update the focal resource of the owning system to reflect the content specified as the Task.focus
   */
  ChangeTheFocalResource: task_code_ChangeTheFocalResource,
  /**
   * Act to perform the actions defined in the focus request.  This might result in a 'more assertive' request (order for a plan or proposal, filler order for a placer order), but is intend to eventually result in events.  The degree of fulfillment requested might be limited by Task.restriction.
   */
  FulfillTheFocalRequest: task_code_FulfillTheFocalRequest,
  /**
   * Replace the focal resource with the specified input resource
   */
  ReplaceTheFocalResourceWithTheInputResource: task_code_ReplaceTheFocalResourceWithTheInputResource,
  /**
   * Transition the focal resource from 'suspended' to 'active' or 'in-progress' as appropriate for the resource type.
   */
  ReActivateTheFocalResource: task_code_ReActivateTheFocalResource,
  /**
   * Transition the focal resource from 'active' or 'in-progress' to 'suspended'
   */
  SuspendTheFocalResource: task_code_SuspendTheFocalResource,
};
const task_intent_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/task-intent',
};
/**
 * Distinguishes whether the task is a proposal, plan or full order.
 */
export const TaskIntent = {
  /**
   * The request represents the view of an authorization instantiated by a fulfilling system representing the details of the fulfiller's intention to act upon a submitted order.
   */
  FillerOrder_request_intent: request_intent_FillerOrder,
  /**
   * An order created in fulfillment of a broader order that represents the authorization for a single activity occurrence.  E.g. The administration of a single dose of a drug.
   */
  InstanceOrder_request_intent: request_intent_InstanceOrder,
  /**
   * The request represents a component or option for a RequestGroup that establishes timing, conditionality and/or other constraints among a set of requests.  Refer to [[[RequestGroup]]] for additional information on how this status is used.
   */
  Option_request_intent: request_intent_Option,
  /**
   * The request represents a request/demand and authorization for action by a Practitioner.
   */
  Order_request_intent: request_intent_Order,
  /**
   * The request represents an original authorization for action.
   */
  OriginalOrder_request_intent: request_intent_OriginalOrder,
  /**
   * The request represents an intention to ensure something occurs without providing an authorization for others to act.
   */
  Plan_request_intent: request_intent_Plan,
  /**
   * The request is a suggestion made by someone/something that does not have an intention to ensure it occurs and without providing an authorization to act.
   */
  Proposal_request_intent: request_intent_Proposal,
  /**
   * The request represents an automatically generated supplemental authorization for action based on a parent authorization together with initial results of the action taken against that parent authorization.
   */
  ReflexOrder_request_intent: request_intent_ReflexOrder,
  /**
   * The intent is not known.  When dealing with Task, it's not always known (or relevant) how the task was initiated - i.e. whether it was proposed, planned, ordered or just done spontaneously.
   */
  Unknown_task_intent: task_intent_Unknown,
};
const task_status_Accepted: Coding = {
  code: 'accepted',
  display: 'Accepted',
  system: 'http://hl7.org/fhir/task-status',
};
const task_status_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'http://hl7.org/fhir/task-status',
};
const task_status_Completed: Coding = {
  code: 'completed',
  display: 'Completed',
  system: 'http://hl7.org/fhir/task-status',
};
const task_status_Draft: Coding = {
  code: 'draft',
  display: 'Draft',
  system: 'http://hl7.org/fhir/task-status',
};
const task_status_EnteredInError: Coding = {
  code: 'entered-in-error',
  display: 'Entered in Error',
  system: 'http://hl7.org/fhir/task-status',
};
const task_status_Failed: Coding = {
  code: 'failed',
  display: 'Failed',
  system: 'http://hl7.org/fhir/task-status',
};
const task_status_InProgress: Coding = {
  code: 'in-progress',
  display: 'In Progress',
  system: 'http://hl7.org/fhir/task-status',
};
const task_status_OnHold: Coding = {
  code: 'on-hold',
  display: 'On Hold',
  system: 'http://hl7.org/fhir/task-status',
};
const task_status_Ready: Coding = {
  code: 'ready',
  display: 'Ready',
  system: 'http://hl7.org/fhir/task-status',
};
const task_status_Received: Coding = {
  code: 'received',
  display: 'Received',
  system: 'http://hl7.org/fhir/task-status',
};
const task_status_Rejected: Coding = {
  code: 'rejected',
  display: 'Rejected',
  system: 'http://hl7.org/fhir/task-status',
};
const task_status_Requested: Coding = {
  code: 'requested',
  display: 'Requested',
  system: 'http://hl7.org/fhir/task-status',
};
/**
 * The current status of the task.
 */
export const TaskStatus = {
  /**
   * The potential performer has agreed to execute the task but has not yet started work.
   */
  Accepted: task_status_Accepted,
  /**
   * The task was not completed.
   */
  Cancelled: task_status_Cancelled,
  /**
   * The task has been completed.
   */
  Completed: task_status_Completed,
  /**
   * The task is not yet ready to be acted upon.
   */
  Draft: task_status_Draft,
  /**
   * The task should never have existed and is retained only because of the possibility it may have used.
   */
  EnteredInError: task_status_EnteredInError,
  /**
   * The task was attempted but could not be completed due to some error.
   */
  Failed: task_status_Failed,
  /**
   * The task has been started but is not yet complete.
   */
  InProgress: task_status_InProgress,
  /**
   * The task has been started but work has been paused.
   */
  OnHold: task_status_OnHold,
  /**
   * The task is ready to be performed, but no action has yet been taken.  Used in place of requested/received/accepted/rejected when request assignment and acceptance is a given.
   */
  Ready: task_status_Ready,
  /**
   * A potential performer has claimed ownership of the task and is evaluating whether to perform it.
   */
  Received: task_status_Received,
  /**
   * The potential performer who claimed ownership of the task has decided not to execute it prior to performing any action.
   */
  Rejected: task_status_Rejected,
  /**
   * The task is ready to be acted upon and action is sought.
   */
  Requested: task_status_Requested,
};
const teeth_VAL11: Coding = {
  code: '11',
  display: '11',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL12: Coding = {
  code: '12',
  display: '12',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL13: Coding = {
  code: '13',
  display: '13',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL14: Coding = {
  code: '14',
  display: '14',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL15: Coding = {
  code: '15',
  display: '15',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL16: Coding = {
  code: '16',
  display: '16',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL17: Coding = {
  code: '17',
  display: '17',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL18: Coding = {
  code: '18',
  display: '18',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL21: Coding = {
  code: '21',
  display: '21',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL22: Coding = {
  code: '22',
  display: '22',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL23: Coding = {
  code: '23',
  display: '23',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL24: Coding = {
  code: '24',
  display: '24',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL25: Coding = {
  code: '25',
  display: '25',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL26: Coding = {
  code: '26',
  display: '26',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL27: Coding = {
  code: '27',
  display: '27',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL28: Coding = {
  code: '28',
  display: '28',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL31: Coding = {
  code: '31',
  display: '31',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL32: Coding = {
  code: '32',
  display: '32',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL33: Coding = {
  code: '33',
  display: '33',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL34: Coding = {
  code: '34',
  display: '34',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL35: Coding = {
  code: '35',
  display: '35',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL36: Coding = {
  code: '36',
  display: '36',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL37: Coding = {
  code: '37',
  display: '37',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL38: Coding = {
  code: '38',
  display: '38',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL41: Coding = {
  code: '41',
  display: '41',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL42: Coding = {
  code: '42',
  display: '42',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL43: Coding = {
  code: '43',
  display: '43',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL44: Coding = {
  code: '44',
  display: '44',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL45: Coding = {
  code: '45',
  display: '45',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL46: Coding = {
  code: '46',
  display: '46',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL47: Coding = {
  code: '47',
  display: '47',
  system: 'http://hl7.org/fhir/ex-fdi',
};
const teeth_VAL48: Coding = {
  code: '48',
  display: '48',
  system: 'http://hl7.org/fhir/ex-fdi',
};
/**
 * This value set includes the FDI Teeth codes.
 */
export const Teeth = {
  /**
   * Upper Right Tooth 1 from the central axis, permanent dentition.
   */
  VAL11: teeth_VAL11,
  /**
   * Upper Right Tooth 2 from the central axis, permanent dentition.
   */
  VAL12: teeth_VAL12,
  /**
   * Upper Right Tooth 3 from the central axis, permanent dentition.
   */
  VAL13: teeth_VAL13,
  /**
   * Upper Right Tooth 4 from the central axis, permanent dentition.
   */
  VAL14: teeth_VAL14,
  /**
   * Upper Right Tooth 5 from the central axis, permanent dentition.
   */
  VAL15: teeth_VAL15,
  /**
   * Upper Right Tooth 6 from the central axis, permanent dentition.
   */
  VAL16: teeth_VAL16,
  /**
   * Upper Right Tooth 7 from the central axis, permanent dentition.
   */
  VAL17: teeth_VAL17,
  /**
   * Upper Right Tooth 8 from the central axis, permanent dentition.
   */
  VAL18: teeth_VAL18,
  /**
   * Upper Left Tooth 1 from the central axis, permanent dentition.
   */
  VAL21: teeth_VAL21,
  /**
   * Upper Left Tooth 2 from the central axis, permanent dentition.
   */
  VAL22: teeth_VAL22,
  /**
   * Upper Left Tooth 3 from the central axis, permanent dentition.
   */
  VAL23: teeth_VAL23,
  /**
   * Upper Left Tooth 4 from the central axis, permanent dentition.
   */
  VAL24: teeth_VAL24,
  /**
   * Upper Left Tooth 5 from the central axis, permanent dentition.
   */
  VAL25: teeth_VAL25,
  /**
   * Upper Left Tooth 6 from the central axis, permanent dentition.
   */
  VAL26: teeth_VAL26,
  /**
   * Upper Left Tooth 7 from the central axis, permanent dentition.
   */
  VAL27: teeth_VAL27,
  /**
   * Upper Left Tooth 8 from the central axis, permanent dentition.
   */
  VAL28: teeth_VAL28,
  /**
   * Lower Left Tooth 1 from the central axis, permanent dentition.
   */
  VAL31: teeth_VAL31,
  /**
   * Lower Left Tooth 2 from the central axis, permanent dentition.
   */
  VAL32: teeth_VAL32,
  /**
   * Lower Left Tooth 3 from the central axis, permanent dentition.
   */
  VAL33: teeth_VAL33,
  /**
   * Lower Left Tooth 4 from the central axis, permanent dentition.
   */
  VAL34: teeth_VAL34,
  /**
   * Lower Left Tooth 5 from the central axis, permanent dentition.
   */
  VAL35: teeth_VAL35,
  /**
   * Lower Left Tooth 6 from the central axis, permanent dentition.
   */
  VAL36: teeth_VAL36,
  /**
   * Lower Left Tooth 7 from the central axis, permanent dentition.
   */
  VAL37: teeth_VAL37,
  /**
   * Lower Left Tooth 8 from the central axis, permanent dentition.
   */
  VAL38: teeth_VAL38,
  /**
   * Lower Right Tooth 1 from the central axis, permanent dentition.
   */
  VAL41: teeth_VAL41,
  /**
   * Lower Right Tooth 2 from the central axis, permanent dentition.
   */
  VAL42: teeth_VAL42,
  /**
   * Lower Right Tooth 3 from the central axis, permanent dentition.
   */
  VAL43: teeth_VAL43,
  /**
   * Lower Right Tooth 4 from the central axis, permanent dentition.
   */
  VAL44: teeth_VAL44,
  /**
   * Lower Right Tooth 5 from the central axis, permanent dentition.
   */
  VAL45: teeth_VAL45,
  /**
   * Lower Right Tooth 6 from the central axis, permanent dentition.
   */
  VAL46: teeth_VAL46,
  /**
   * Lower Right Tooth 7 from the central axis, permanent dentition.
   */
  VAL47: teeth_VAL47,
  /**
   * Lower Right Tooth 8 from the central axis, permanent dentition.
   */
  VAL48: teeth_VAL48,
};
const tldc_Active: Coding = {
  code: 'active',
  display: 'active',
  system: 'urn:oid:2.16.840.1.113883.3.1937.98.5.8',
};
const tldc_Cancelled: Coding = {
  code: 'cancelled',
  display: 'Cancelled',
  system: 'urn:oid:2.16.840.1.113883.3.1937.98.5.8',
};
const tldc_Draft: Coding = {
  code: 'draft',
  display: 'Draft',
  system: 'urn:oid:2.16.840.1.113883.3.1937.98.5.8',
};
const tldc_UnderPrePublicationReview: Coding = {
  code: 'pending',
  display: 'Under pre-publication review',
  system: 'urn:oid:2.16.840.1.113883.3.1937.98.5.8',
};
const tldc_Rejected: Coding = {
  code: 'rejected',
  display: 'Rejected',
  system: 'urn:oid:2.16.840.1.113883.3.1937.98.5.8',
};
const tldc_Retired: Coding = {
  code: 'retired',
  display: 'retired',
  system: 'urn:oid:2.16.840.1.113883.3.1937.98.5.8',
};
const tldc_InReview: Coding = {
  code: 'review',
  display: 'In Review',
  system: 'urn:oid:2.16.840.1.113883.3.1937.98.5.8',
};
const tldc_Terminated: Coding = {
  code: 'terminated',
  display: 'Terminated',
  system: 'urn:oid:2.16.840.1.113883.3.1937.98.5.8',
};
/**
 * The status indicates the level of maturity of the design and may be used to manage the use of the design.
 */
export const TemplateStatusCode = {
  /**
   * Design has been deemed fit for the intended purpose and is published by the governance group.
   */
  Active: tldc_Active,
  /**
   * A drafted design is determined to be erroneous or not fit for intended purpose and is discontinued before ever being published in an active state.
   */
  Cancelled: tldc_Cancelled,
  /**
   * Design is under development (nascent).
   */
  Draft: tldc_Draft,
  /**
   * Design is completed and is being reviewed.
   */
  UnderPrePublicationReview: tldc_UnderPrePublicationReview,
  /**
   * A previously drafted design is determined to be erroneous or not fit for intended purpose and is discontinued before ever being published for consideration in a pending state.
   */
  Rejected: tldc_Rejected,
  /**
   * A previously active design is discontinued from use. It should no longer be used for future designs, but for historical purposes may be used to process data previously recorded using this design. A newer design may or may not exist. The design is published in the retired state.
   */
  Retired: tldc_Retired,
  /**
   * Design is active, but is under review. The review may result in a change to the design. The change may necessitate a new version to be created. This in turn may result in the prior version of the template to be retired. Alternatively, the review may result in a change to the design that does not require a new version to be created, or it may result in no change to the design at all.
   */
  InReview: tldc_InReview,
  /**
   * A design is determined to be erroneous or not fit for the intended purpose and should no longer be used, even for historical purposes. No new designs can be developed for this template. The associated template no longer needs to be published, but if published, is shown in the terminated state.
   */
  Terminated: tldc_Terminated,
};
const testscript_operation_codes_Apply: Coding = {
  code: 'apply',
  display: '$apply',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Batch: Coding = {
  code: 'batch',
  display: 'Batch',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Capabilities: Coding = {
  code: 'capabilities',
  display: 'Capabilities',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Closure: Coding = {
  code: 'closure',
  display: '$closure',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Conforms: Coding = {
  code: 'conforms',
  display: '$conforms',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Create: Coding = {
  code: 'create',
  display: 'Create',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_DataRequirements: Coding = {
  code: 'data-requirements',
  display: '$data-requirements',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Delete: Coding = {
  code: 'delete',
  display: 'Delete',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_DeleteCondMultiple: Coding = {
  code: 'deleteCondMultiple',
  display: 'Conditional Delete Multiple',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_DeleteCondSingle: Coding = {
  code: 'deleteCondSingle',
  display: 'Conditional Delete Single',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Document: Coding = {
  code: 'document',
  display: '$document',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Evaluate: Coding = {
  code: 'evaluate',
  display: '$evaluate',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_EvaluateMeasure: Coding = {
  code: 'evaluate-measure',
  display: '$evaluate-measure',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Everything: Coding = {
  code: 'everything',
  display: '$everything',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Expand: Coding = {
  code: 'expand',
  display: '$expand',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Find: Coding = {
  code: 'find',
  display: '$find',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_FindMatches: Coding = {
  code: 'find-matches',
  display: '$find-matches',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Graphql: Coding = {
  code: 'graphql',
  display: '$graphql',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_History: Coding = {
  code: 'history',
  display: 'History',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Implements: Coding = {
  code: 'implements',
  display: '$implements',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Lastn: Coding = {
  code: 'lastn',
  display: '$lastn',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Lookup: Coding = {
  code: 'lookup',
  display: '$lookup',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Match: Coding = {
  code: 'match',
  display: '$match',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Meta: Coding = {
  code: 'meta',
  display: '$meta',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_MetaAdd: Coding = {
  code: 'meta-add',
  display: '$meta-add',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_MetaDelete: Coding = {
  code: 'meta-delete',
  display: '$meta-delete',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Patch: Coding = {
  code: 'patch',
  display: 'Patch',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Populate: Coding = {
  code: 'populate',
  display: '$populate',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Populatehtml: Coding = {
  code: 'populatehtml',
  display: '$populatehtml',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Populatelink: Coding = {
  code: 'populatelink',
  display: '$populatelink',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_ProcessMessage: Coding = {
  code: 'process-message',
  display: '$process-message',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Questionnaire: Coding = {
  code: 'questionnaire',
  display: '$questionnaire',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Read: Coding = {
  code: 'read',
  display: 'Read',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Search: Coding = {
  code: 'search',
  display: 'Search',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Stats: Coding = {
  code: 'stats',
  display: '$stats',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Subset: Coding = {
  code: 'subset',
  display: '$subset',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Subsumes: Coding = {
  code: 'subsumes',
  display: '$subsumes',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Transaction: Coding = {
  code: 'transaction',
  display: 'Transaction',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Transform: Coding = {
  code: 'transform',
  display: '$transform',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Translate: Coding = {
  code: 'translate',
  display: '$translate',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Update: Coding = {
  code: 'update',
  display: 'Update',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_UpdateCreate: Coding = {
  code: 'updateCreate',
  display: 'Create using Update',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Validate: Coding = {
  code: 'validate',
  display: '$validate',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_ValidateCode: Coding = {
  code: 'validate-code',
  display: '$validate-code',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
const testscript_operation_codes_Vread: Coding = {
  code: 'vread',
  display: 'Version Read',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-operation-codes',
};
/**
 * This value set defines a set of codes that are used to indicate the supported operations of a testing engine or tool.
 */
export const TestscriptOperationCodes = {
  /**
   * Realizes an ActivityDefinition in a specific context
   */
  Apply: testscript_operation_codes_Apply,
  /**
   * Update, create or delete a set of resources as independent actions.
   */
  Batch: testscript_operation_codes_Batch,
  /**
   * Get a capability statement for the system.
   */
  Capabilities: testscript_operation_codes_Capabilities,
  /**
   * Closure Table Maintenance
   */
  Closure: testscript_operation_codes_Closure,
  /**
   * Compare two systems CapabilityStatements
   */
  Conforms: testscript_operation_codes_Conforms,
  /**
   * Create a new resource with a server assigned id.
   */
  Create: testscript_operation_codes_Create,
  /**
   * Aggregates and returns the parameters and data requirements for a resource and all its dependencies as a single module definition
   */
  DataRequirements: testscript_operation_codes_DataRequirements,
  /**
   * Delete a resource.
   */
  Delete: testscript_operation_codes_Delete,
  /**
   * Conditionally delete one or more resources based on search parameters.
   */
  DeleteCondMultiple: testscript_operation_codes_DeleteCondMultiple,
  /**
   * Conditionally delete a single resource based on search parameters.
   */
  DeleteCondSingle: testscript_operation_codes_DeleteCondSingle,
  /**
   * Generate a Document
   */
  Document: testscript_operation_codes_Document,
  /**
   * Request clinical decision support guidance based on a specific decision support module
   */
  Evaluate: testscript_operation_codes_Evaluate,
  /**
   * Invoke an eMeasure and obtain the results
   */
  EvaluateMeasure: testscript_operation_codes_EvaluateMeasure,
  /**
   * Return all the related information as described in the Encounter or Patient
   */
  Everything: testscript_operation_codes_Everything,
  /**
   * Value Set Expansion
   */
  Expand: testscript_operation_codes_Expand,
  /**
   * Find a functional list
   */
  Find: testscript_operation_codes_Find,
  /**
   * Finding Codes based on supplied properties
   */
  FindMatches: testscript_operation_codes_FindMatches,
  /**
   * Invoke a GraphQL query
   */
  Graphql: testscript_operation_codes_Graphql,
  /**
   * Retrieve the change history for a particular resource or resource type.
   */
  History: testscript_operation_codes_History,
  /**
   * Test if a server implements a client's required operations
   */
  Implements: testscript_operation_codes_Implements,
  /**
   * Last N Observations Query
   */
  Lastn: testscript_operation_codes_Lastn,
  /**
   * Concept Look Up and Decomposition
   */
  Lookup: testscript_operation_codes_Lookup,
  /**
   * Find patient matches using MPI based logic
   */
  Match: testscript_operation_codes_Match,
  /**
   * Access a list of profiles, tags, and security labels
   */
  Meta: testscript_operation_codes_Meta,
  /**
   * Add profiles, tags, and security labels to a resource
   */
  MetaAdd: testscript_operation_codes_MetaAdd,
  /**
   * Delete profiles, tags, and security labels for a resource
   */
  MetaDelete: testscript_operation_codes_MetaDelete,
  /**
   * Patch an existing resource by its id.
   */
  Patch: testscript_operation_codes_Patch,
  /**
   * Populate Questionnaire
   */
  Populate: testscript_operation_codes_Populate,
  /**
   * Generate HTML for Questionnaire
   */
  Populatehtml: testscript_operation_codes_Populatehtml,
  /**
   * Generate a link to a Questionnaire completion webpage
   */
  Populatelink: testscript_operation_codes_Populatelink,
  /**
   * Process a message according to the defined event
   */
  ProcessMessage: testscript_operation_codes_ProcessMessage,
  /**
   * Build Questionnaire
   */
  Questionnaire: testscript_operation_codes_Questionnaire,
  /**
   * Read the current state of the resource.
   */
  Read: testscript_operation_codes_Read,
  /**
   * Search based on some filter criteria.
   */
  Search: testscript_operation_codes_Search,
  /**
   * Observation Statistics
   */
  Stats: testscript_operation_codes_Stats,
  /**
   * Fetch a subset of the CapabilityStatement resource
   */
  Subset: testscript_operation_codes_Subset,
  /**
   * CodeSystem Subsumption Testing
   */
  Subsumes: testscript_operation_codes_Subsumes,
  /**
   * Update, create or delete a set of resources as a single transaction.
   */
  Transaction: testscript_operation_codes_Transaction,
  /**
   * Model Instance Transformation
   */
  Transform: testscript_operation_codes_Transform,
  /**
   * Concept Translation
   */
  Translate: testscript_operation_codes_Translate,
  /**
   * Update an existing resource by its id.
   */
  Update: testscript_operation_codes_Update,
  /**
   * Update an existing resource by its id (or create it if it is new).
   */
  UpdateCreate: testscript_operation_codes_UpdateCreate,
  /**
   * Validate a resource
   */
  Validate: testscript_operation_codes_Validate,
  /**
   * ValueSet based Validation
   */
  ValidateCode: testscript_operation_codes_ValidateCode,
  /**
   * Read the state of a specific version of the resource.
   */
  Vread: testscript_operation_codes_Vread,
};
const testscript_profile_destination_types_FHIRSDCFormManager: Coding = {
  code: 'FHIR-SDC-FormManager',
  display: 'FHIR SDC FormManager',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-profile-destination-types',
};
const testscript_profile_destination_types_FHIRSDCFormProcessor: Coding = {
  code: 'FHIR-SDC-FormProcessor',
  display: 'FHIR SDC FormProcessor',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-profile-destination-types',
};
const testscript_profile_destination_types_FHIRSDCFormReceiver: Coding = {
  code: 'FHIR-SDC-FormReceiver',
  display: 'FHIR SDC FormReceiver',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-profile-destination-types',
};
const testscript_profile_destination_types_FHIRServer: Coding = {
  code: 'FHIR-Server',
  display: 'FHIR Server',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-profile-destination-types',
};
/**
 * This value set defines a set of codes that are used to indicate the profile type of a test system when acting as the destination within a TestScript.
 */
export const TestscriptProfileDestinationTypes = {
  /**
   * A FHIR server acting as a Structured Data Capture Form Manager.
   */
  FHIRSDCFormManager: testscript_profile_destination_types_FHIRSDCFormManager,
  /**
   * A FHIR server acting as a Structured Data Capture Form Processor.
   */
  FHIRSDCFormProcessor: testscript_profile_destination_types_FHIRSDCFormProcessor,
  /**
   * A FHIR server acting as a Structured Data Capture Form Receiver.
   */
  FHIRSDCFormReceiver: testscript_profile_destination_types_FHIRSDCFormReceiver,
  /**
   * General FHIR server used to respond to operations sent from a FHIR client.
   */
  FHIRServer: testscript_profile_destination_types_FHIRServer,
};
const testscript_profile_origin_types_FHIRClient: Coding = {
  code: 'FHIR-Client',
  display: 'FHIR Client',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-profile-origin-types',
};
const testscript_profile_origin_types_FHIRSDCFormFiller: Coding = {
  code: 'FHIR-SDC-FormFiller',
  display: 'FHIR SDC FormFiller',
  system: 'http://terminology.hl7.org/CodeSystem/testscript-profile-origin-types',
};
/**
 * This value set defines a set of codes that are used to indicate the profile type of a test system when acting as the origin within a TestScript.
 */
export const TestscriptProfileOriginTypes = {
  /**
   * General FHIR client used to initiate operations against a FHIR server.
   */
  FHIRClient: testscript_profile_origin_types_FHIRClient,
  /**
   * A FHIR client acting as a Structured Data Capture Form Filler.
   */
  FHIRSDCFormFiller: testscript_profile_origin_types_FHIRSDCFormFiller,
};
const TextureCode_ChoppedFood: Coding = {
  code: '228049004',
  display: 'Chopped food',
  system: 'http://snomed.info/sct',
};
const TextureCode_CutUpFood: Coding = {
  code: '228053002',
  display: 'Cut-up food',
  system: 'http://snomed.info/sct',
};
const TextureCode_LiquidizedFood: Coding = {
  code: '228055009',
  display: 'Liquidized food',
  system: 'http://snomed.info/sct',
};
const TextureCode_LumpyFood: Coding = {
  code: '228056005',
  display: 'Lumpy food',
  system: 'http://snomed.info/sct',
};
const TextureCode_SemiSolidFood: Coding = {
  code: '228057001',
  display: 'Semi-solid food',
  system: 'http://snomed.info/sct',
};
const TextureCode_SingleTextureFood: Coding = {
  code: '228058006',
  display: 'Single texture food',
  system: 'http://snomed.info/sct',
};
const TextureCode_SoftFood: Coding = {
  code: '228059003',
  display: 'Soft food',
  system: 'http://snomed.info/sct',
};
const TextureCode_SolidFood: Coding = {
  code: '228060008',
  display: 'Solid food',
  system: 'http://snomed.info/sct',
};
const TextureCode_EasyToChewFood: Coding = {
  code: '439091000124107',
  display: 'Easy to chew food',
  system: 'http://snomed.info/sct',
};
const TextureCode_MashedFood: Coding = {
  code: '441751000124100',
  display: 'Mashed food',
  system: 'http://snomed.info/sct',
};
const TextureCode_MincedFood: Coding = {
  code: '441761000124103',
  display: 'Minced food',
  system: 'http://snomed.info/sct',
};
const TextureCode_MoistFood: Coding = {
  code: '441771000124105',
  display: 'Moist food',
  system: 'http://snomed.info/sct',
};
const TextureCode_StrainedFood: Coding = {
  code: '441791000124106',
  display: 'Strained food',
  system: 'http://snomed.info/sct',
};
const TextureCode_GroundFood: Coding = {
  code: '441881000124103',
  display: 'Ground food',
  system: 'http://snomed.info/sct',
};
/**
 * TextureModifier: Codes for food consistency types or texture modifications to apply to foods. This value set is composed of SNOMED CT (US Extension and Core) Concepts from SCTID 229961002 Food consistency types (substance) hierarchy and is provided as a suggestive example.
 */
export const TextureCode = {
  ChoppedFood: TextureCode_ChoppedFood,
  CutUpFood: TextureCode_CutUpFood,
  LiquidizedFood: TextureCode_LiquidizedFood,
  LumpyFood: TextureCode_LumpyFood,
  SemiSolidFood: TextureCode_SemiSolidFood,
  SingleTextureFood: TextureCode_SingleTextureFood,
  SoftFood: TextureCode_SoftFood,
  SolidFood: TextureCode_SolidFood,
  EasyToChewFood: TextureCode_EasyToChewFood,
  MashedFood: TextureCode_MashedFood,
  MincedFood: TextureCode_MincedFood,
  MoistFood: TextureCode_MoistFood,
  StrainedFood: TextureCode_StrainedFood,
  GroundFood: TextureCode_GroundFood,
};
const v3_GTSAbbreviation_AM: Coding = {
  code: 'AM',
  display: 'AM',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_AtBedtime: Coding = {
  code: 'BED',
  display: 'at bedtime',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_BID: Coding = {
  code: 'BID',
  display: 'BID',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_Monthly: Coding = {
  code: 'MO',
  display: 'monthly',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_PM: Coding = {
  code: 'PM',
  display: 'PM',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_EveryHour: Coding = {
  code: 'Q1H',
  display: 'every hour',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_Every2Hours: Coding = {
  code: 'Q2H',
  display: 'every 2 hours',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_Every3Hours: Coding = {
  code: 'Q3H',
  display: 'every 3 hours',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_Q4H: Coding = {
  code: 'Q4H',
  display: 'Q4H',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_Q6H: Coding = {
  code: 'Q6H',
  display: 'Q6H',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_Every8Hours: Coding = {
  code: 'Q8H',
  display: 'every 8 hours',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_QD: Coding = {
  code: 'QD',
  display: 'QD',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_QID: Coding = {
  code: 'QID',
  display: 'QID',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_QOD: Coding = {
  code: 'QOD',
  display: 'QOD',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_TID: Coding = {
  code: 'TID',
  display: 'TID',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_Weekly: Coding = {
  code: 'WK',
  display: 'weekly',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
/**
 * Code for a known / defined timing pattern.
 */
export const TimingAbbreviation = {
  /**
   * Every morning at institution specified times.
   */
  AM: v3_GTSAbbreviation_AM,
  /**
   * At bedtime (institution specified time).
   */
  AtBedtime: v3_GTSAbbreviation_AtBedtime,
  /**
   * Two times a day at institution specified time
   */
  BID: v3_GTSAbbreviation_BID,
  /**
   * Monthly at institution specified time.
   */
  Monthly: v3_GTSAbbreviation_Monthly,
  /**
   * Every afternoon at institution specified times.
   */
  PM: v3_GTSAbbreviation_PM,
  /**
   * Every hour at institution specified times.
   */
  EveryHour: v3_GTSAbbreviation_EveryHour,
  /**
   * Every 2 hours at institution specified times.
   */
  Every2Hours: v3_GTSAbbreviation_Every2Hours,
  /**
   * Every 3 hours at institution specified times.
   */
  Every3Hours: v3_GTSAbbreviation_Every3Hours,
  /**
   * Every 4 hours at institution specified time
   */
  Q4H: v3_GTSAbbreviation_Q4H,
  /**
   * Every 6 hours at institution specified time
   */
  Q6H: v3_GTSAbbreviation_Q6H,
  /**
   * Every 8 hours at institution specified times.
   */
  Every8Hours: v3_GTSAbbreviation_Every8Hours,
  /**
   * Every day at institution specified times.
   */
  QD: v3_GTSAbbreviation_QD,
  /**
   * Four times a day at institution specified time
   */
  QID: v3_GTSAbbreviation_QID,
  /**
   * Every other day at institution specified times.
   */
  QOD: v3_GTSAbbreviation_QOD,
  /**
   * Three times a day at institution specified time
   */
  TID: v3_GTSAbbreviation_TID,
  /**
   * Weekly at institution specified time.
   */
  Weekly: v3_GTSAbbreviation_Weekly,
};
const tooth_OralCavity: Coding = {
  code: '0',
  display: 'Oral cavity',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL1: Coding = {
  code: '1',
  display: '1',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL11: Coding = {
  code: '11',
  display: '11',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL12: Coding = {
  code: '12',
  display: '12',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL13: Coding = {
  code: '13',
  display: '13',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL14: Coding = {
  code: '14',
  display: '14',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL15: Coding = {
  code: '15',
  display: '15',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL16: Coding = {
  code: '16',
  display: '16',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL17: Coding = {
  code: '17',
  display: '17',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL18: Coding = {
  code: '18',
  display: '18',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL2: Coding = {
  code: '2',
  display: '2',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL21: Coding = {
  code: '21',
  display: '21',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL22: Coding = {
  code: '22',
  display: '22',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL23: Coding = {
  code: '23',
  display: '23',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL24: Coding = {
  code: '24',
  display: '24',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL25: Coding = {
  code: '25',
  display: '25',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL26: Coding = {
  code: '26',
  display: '26',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL27: Coding = {
  code: '27',
  display: '27',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL28: Coding = {
  code: '28',
  display: '28',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL3: Coding = {
  code: '3',
  display: '3',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL31: Coding = {
  code: '31',
  display: '31',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL32: Coding = {
  code: '32',
  display: '32',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL33: Coding = {
  code: '33',
  display: '33',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL34: Coding = {
  code: '34',
  display: '34',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL35: Coding = {
  code: '35',
  display: '35',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL36: Coding = {
  code: '36',
  display: '36',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL37: Coding = {
  code: '37',
  display: '37',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL38: Coding = {
  code: '38',
  display: '38',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL4: Coding = {
  code: '4',
  display: '4',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL41: Coding = {
  code: '41',
  display: '41',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL42: Coding = {
  code: '42',
  display: '42',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL43: Coding = {
  code: '43',
  display: '43',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL44: Coding = {
  code: '44',
  display: '44',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL45: Coding = {
  code: '45',
  display: '45',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL46: Coding = {
  code: '46',
  display: '46',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL47: Coding = {
  code: '47',
  display: '47',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL48: Coding = {
  code: '48',
  display: '48',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL5: Coding = {
  code: '5',
  display: '5',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL6: Coding = {
  code: '6',
  display: '6',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL7: Coding = {
  code: '7',
  display: '7',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
const tooth_VAL8: Coding = {
  code: '8',
  display: '8',
  system: 'http://terminology.hl7.org/CodeSystem/ex-tooth',
};
/**
 * This value set includes a smattering of FDI oral site codes.
 */
export const Tooth = {
  /**
   * Oral cavity.
   */
  OralCavity: tooth_OralCavity,
  /**
   * Permanent teeth Maxillary right.
   */
  VAL1: tooth_VAL1,
  /**
   * Upper Right Tooth 1 from the central axis, permanent dentition.
   */
  VAL11: tooth_VAL11,
  /**
   * Upper Right Tooth 2 from the central axis, permanent dentition.
   */
  VAL12: tooth_VAL12,
  /**
   * Upper Right Tooth 3 from the central axis, permanent dentition.
   */
  VAL13: tooth_VAL13,
  /**
   * Upper Right Tooth 4 from the central axis, permanent dentition.
   */
  VAL14: tooth_VAL14,
  /**
   * Upper Right Tooth 5 from the central axis, permanent dentition.
   */
  VAL15: tooth_VAL15,
  /**
   * Upper Right Tooth 6 from the central axis, permanent dentition.
   */
  VAL16: tooth_VAL16,
  /**
   * Upper Right Tooth 7 from the central axis, permanent dentition.
   */
  VAL17: tooth_VAL17,
  /**
   * Upper Right Tooth 8 from the central axis, permanent dentition.
   */
  VAL18: tooth_VAL18,
  /**
   * Permanent teeth Maxillary left.
   */
  VAL2: tooth_VAL2,
  /**
   * Upper Left Tooth 1 from the central axis, permanent dentition.
   */
  VAL21: tooth_VAL21,
  /**
   * Upper Left Tooth 2 from the central axis, permanent dentition.
   */
  VAL22: tooth_VAL22,
  /**
   * Upper Left Tooth 3 from the central axis, permanent dentition.
   */
  VAL23: tooth_VAL23,
  /**
   * Upper Left Tooth 4 from the central axis, permanent dentition.
   */
  VAL24: tooth_VAL24,
  /**
   * Upper Left Tooth 5 from the central axis, permanent dentition.
   */
  VAL25: tooth_VAL25,
  /**
   * Upper Left Tooth 6 from the central axis, permanent dentition.
   */
  VAL26: tooth_VAL26,
  /**
   * Upper Left Tooth 7 from the central axis, permanent dentition.
   */
  VAL27: tooth_VAL27,
  /**
   * Upper Left Tooth 8 from the central axis, permanent dentition.
   */
  VAL28: tooth_VAL28,
  /**
   * Permanent teeth Mandibular right.
   */
  VAL3: tooth_VAL3,
  /**
   * Lower Left Tooth 1 from the central axis, permanent dentition.
   */
  VAL31: tooth_VAL31,
  /**
   * Lower Left Tooth 2 from the central axis, permanent dentition.
   */
  VAL32: tooth_VAL32,
  /**
   * Lower Left Tooth 3 from the central axis, permanent dentition.
   */
  VAL33: tooth_VAL33,
  /**
   * Lower Left Tooth 4 from the central axis, permanent dentition.
   */
  VAL34: tooth_VAL34,
  /**
   * Lower Left Tooth 5 from the central axis, permanent dentition.
   */
  VAL35: tooth_VAL35,
  /**
   * Lower Left Tooth 6 from the central axis, permanent dentition.
   */
  VAL36: tooth_VAL36,
  /**
   * Lower Left Tooth 7 from the central axis, permanent dentition.
   */
  VAL37: tooth_VAL37,
  /**
   * Lower Left Tooth 8 from the central axis, permanent dentition.
   */
  VAL38: tooth_VAL38,
  /**
   * Permanent teeth Mandibular left.
   */
  VAL4: tooth_VAL4,
  /**
   * Lower Right Tooth 1 from the central axis, permanent dentition.
   */
  VAL41: tooth_VAL41,
  /**
   * Lower Right Tooth 2 from the central axis, permanent dentition.
   */
  VAL42: tooth_VAL42,
  /**
   * Lower Right Tooth 3 from the central axis, permanent dentition.
   */
  VAL43: tooth_VAL43,
  /**
   * Lower Right Tooth 4 from the central axis, permanent dentition.
   */
  VAL44: tooth_VAL44,
  /**
   * Lower Right Tooth 5 from the central axis, permanent dentition.
   */
  VAL45: tooth_VAL45,
  /**
   * Lower Right Tooth 6 from the central axis, permanent dentition.
   */
  VAL46: tooth_VAL46,
  /**
   * Lower Right Tooth 7 from the central axis, permanent dentition.
   */
  VAL47: tooth_VAL47,
  /**
   * Lower Right Tooth 8 from the central axis, permanent dentition.
   */
  VAL48: tooth_VAL48,
  /**
   * Deciduous teeth Maxillary right.
   */
  VAL5: tooth_VAL5,
  /**
   * Deciduous teeth Maxillary left.
   */
  VAL6: tooth_VAL6,
  /**
   * Deciduous teeth Mandibular right.
   */
  VAL7: tooth_VAL7,
  /**
   * Deciduous teeth Mandibular left.
   */
  VAL8: tooth_VAL8,
};
const transaction_mode_BatchesSupported: Coding = {
  code: 'batch',
  display: 'Batches supported',
  system: 'http://hl7.org/fhir/transaction-mode',
};
const transaction_mode_BatchesAndTransactions: Coding = {
  code: 'both',
  display: 'Batches & Transactions',
  system: 'http://hl7.org/fhir/transaction-mode',
};
const transaction_mode_None: Coding = {
  code: 'not-supported',
  display: 'None',
  system: 'http://hl7.org/fhir/transaction-mode',
};
const transaction_mode_TransactionsSupported: Coding = {
  code: 'transaction',
  display: 'Transactions Supported',
  system: 'http://hl7.org/fhir/transaction-mode',
};
/**
 * A code that indicates how transactions are supported.
 */
export const TransactionMode = {
  /**
   * Batches are  supported.
   */
  BatchesSupported: transaction_mode_BatchesSupported,
  /**
   * Both batches and transactions are supported.
   */
  BatchesAndTransactions: transaction_mode_BatchesAndTransactions,
  /**
   * Neither batch or transaction is supported.
   */
  None: transaction_mode_None,
  /**
   * Transactions are supported.
   */
  TransactionsSupported: transaction_mode_TransactionsSupported,
};
const trigger_type_DataAccessed: Coding = {
  code: 'data-accessed',
  display: 'Data Accessed',
  system: 'http://hl7.org/fhir/trigger-type',
};
const trigger_type_DataAccessEnded: Coding = {
  code: 'data-access-ended',
  display: 'Data Access Ended',
  system: 'http://hl7.org/fhir/trigger-type',
};
const trigger_type_DataAdded: Coding = {
  code: 'data-added',
  display: 'Data Added',
  system: 'http://hl7.org/fhir/trigger-type',
};
const trigger_type_DataChanged: Coding = {
  code: 'data-changed',
  display: 'Data Changed',
  system: 'http://hl7.org/fhir/trigger-type',
};
const trigger_type_DataUpdated: Coding = {
  code: 'data-modified',
  display: 'Data Updated',
  system: 'http://hl7.org/fhir/trigger-type',
};
const trigger_type_DataRemoved: Coding = {
  code: 'data-removed',
  display: 'Data Removed',
  system: 'http://hl7.org/fhir/trigger-type',
};
const trigger_type_NamedEvent: Coding = {
  code: 'named-event',
  display: 'Named Event',
  system: 'http://hl7.org/fhir/trigger-type',
};
const trigger_type_Periodic: Coding = {
  code: 'periodic',
  display: 'Periodic',
  system: 'http://hl7.org/fhir/trigger-type',
};
/**
 * The type of trigger.
 */
export const TriggerType = {
  /**
   * The trigger occurs whenever data of a particular type is accessed.
   */
  DataAccessed: trigger_type_DataAccessed,
  /**
   * The trigger occurs whenever access to data of a particular type is completed.
   */
  DataAccessEnded: trigger_type_DataAccessEnded,
  /**
   * The trigger occurs whenever data of a particular type is added.
   */
  DataAdded: trigger_type_DataAdded,
  /**
   * The trigger occurs whenever data of a particular type is changed in any way, either added, modified, or removed.
   */
  DataChanged: trigger_type_DataChanged,
  /**
   * The trigger occurs whenever data of a particular type is modified.
   */
  DataUpdated: trigger_type_DataUpdated,
  /**
   * The trigger occurs whenever data of a particular type is removed.
   */
  DataRemoved: trigger_type_DataRemoved,
  /**
   * The trigger occurs in response to a specific named event, and no other information about the trigger is specified. Named events are completely pre-coordinated, and the formal semantics of the trigger are not provided.
   */
  NamedEvent: trigger_type_NamedEvent,
  /**
   * The trigger occurs at a specific time or periodically as described by a timing or schedule. A periodic event cannot have any data elements, but may have a name assigned as a shorthand for the event.
   */
  Periodic: trigger_type_Periodic,
};
const type_derivation_rule_Constraint: Coding = {
  code: 'constraint',
  display: 'Constraint',
  system: 'http://hl7.org/fhir/type-derivation-rule',
};
const type_derivation_rule_Specialization: Coding = {
  code: 'specialization',
  display: 'Specialization',
  system: 'http://hl7.org/fhir/type-derivation-rule',
};
/**
 * How a type relates to its baseDefinition.
 */
export const TypeDerivationRule = {
  /**
   * This definition adds additional rules to an existing concrete type.
   */
  Constraint: type_derivation_rule_Constraint,
  /**
   * This definition defines a new type that adds additional elements to the base type.
   */
  Specialization: type_derivation_rule_Specialization,
};
/**
 * Operations supported by REST at the type or instance level.
 */
export const TypeRestfulInteraction = {
  /**
   * Create a new resource with a server assigned id.
   */
  Create: restful_interaction_Create,
  /**
   * Delete a resource.
   */
  Delete: restful_interaction_Delete,
  /**
   * Retrieve the change history for a particular resource.
   */
  HistoryInstance: restful_interaction_HistoryInstance,
  /**
   * Retrieve the change history for all resources of a particular type.
   */
  HistoryType: restful_interaction_HistoryType,
  /**
   * Update an existing resource by posting a set of changes to it.
   */
  Patch: restful_interaction_Patch,
  /**
   * Read the current state of the resource.
   */
  Read: restful_interaction_Read,
  /**
   * Search all resources of the specified type based on some filter criteria.
   */
  SearchType: restful_interaction_SearchType,
  /**
   * Update an existing resource by its id (or create it if it is new).
   */
  Update: restful_interaction_Update,
  /**
   * Read the state of a specific version of the resource.
   */
  Vread: restful_interaction_Vread,
};
const UcumBodylength_NONE: Coding = {
  code: '[in_i]',
  system: 'http://unitsofmeasure.org',
};
const UcumBodylength_NONE_2: Coding = {
  code: 'cm',
  system: 'http://unitsofmeasure.org',
};
/**
 * UCUM units for recording body length measures such as height and head circumference
 */
export const UcumBodylength = {
  NONE: UcumBodylength_NONE,
  NONE_2: UcumBodylength_NONE_2,
};
const UcumBodytemp_NONE: Coding = {
  code: '[degF]',
  system: 'http://unitsofmeasure.org',
};
const UcumBodytemp_NONE_2: Coding = {
  code: 'Cel',
  system: 'http://unitsofmeasure.org',
};
/**
 * UCUM units for recording Body Temperature
 */
export const UcumBodytemp = {
  NONE: UcumBodytemp_NONE,
  NONE_2: UcumBodytemp_NONE_2,
};
const UcumBodyweight_NONE: Coding = {
  code: '[lb_av]',
  system: 'http://unitsofmeasure.org',
};
const UcumBodyweight_NONE_2: Coding = {
  code: 'g',
  system: 'http://unitsofmeasure.org',
};
const UcumBodyweight_NONE_3: Coding = {
  code: 'kg',
  system: 'http://unitsofmeasure.org',
};
/**
 * UCUM units for recording Body Weight
 */
export const UcumBodyweight = {
  NONE: UcumBodyweight_NONE,
  NONE_2: UcumBodyweight_NONE_2,
  NONE_3: UcumBodyweight_NONE_3,
};
const UcumCommon_Minute: Coding = {
  code: "'",
  display: 'minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Second: Coding = {
  code: "''",
  display: 'second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Percent: Coding = {
  code: '%',
  display: 'percent',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Percent100WBC: Coding = {
  code: '%/100{WBC}',
  display: 'percent / 100 WBC',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentOfSlope: Coding = {
  code: '%[slope]',
  display: 'percent of slope',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Percent0to3Hours: Coding = {
  code: '%{0to3Hours}',
  display: 'percent 0to3Hours',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentAbnormal: Coding = {
  code: '%{abnormal}',
  display: 'percent abnormal',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentAbnormal_2: Coding = {
  code: '%{Abnormal}',
  display: 'percent Abnormal',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentActivity: Coding = {
  code: '%{activity}',
  display: 'percent activity',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentActivity_2: Coding = {
  code: '%{Activity}',
  display: 'percent Activity',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentAggregation: Coding = {
  code: '%{aggregation}',
  display: 'percent aggregation',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentAt60Minute: Coding = {
  code: '%{at_60_min}',
  display: 'percent at 60 minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentOfBacteria: Coding = {
  code: '%{bacteria}',
  display: 'percent of bacteria',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentBasalActivity: Coding = {
  code: '%{basal_activity}',
  display: 'percent basal activity',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentBasalActivity_2: Coding = {
  code: '%{BasalActivity}',
  display: 'percent BasalActivity',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentOfBaseline: Coding = {
  code: '%{baseline}',
  display: 'percent of baseline',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentBinding: Coding = {
  code: '%{binding}',
  display: 'percent binding',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentBinding_2: Coding = {
  code: '%{Binding}',
  display: 'percent Binding',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentBlockade: Coding = {
  code: '%{blockade}',
  display: 'percent blockade',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentBlockade_2: Coding = {
  code: '%{Blockade}',
  display: 'percent Blockade',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentBlocked: Coding = {
  code: '%{blocked}',
  display: 'percent blocked',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentBound: Coding = {
  code: '%{bound}',
  display: 'percent bound',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentBound_2: Coding = {
  code: '%{Bound}',
  display: 'PercentBound',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentBreakdown: Coding = {
  code: '%{breakdown}',
  display: 'percent breakdown',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentCarboxyhemoglobin: Coding = {
  code: '%{Carboxyhemoglobin}',
  display: 'percent Carboxyhemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentOfCells: Coding = {
  code: '%{cells}',
  display: 'percent of cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentConversion: Coding = {
  code: '%{Conversion}',
  display: 'percent Conversion',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentCound: Coding = {
  code: '%{Cound}',
  display: 'percent Cound',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentDeficient: Coding = {
  code: '%{deficient}',
  display: 'percent deficient',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentDose: Coding = {
  code: '%{dose}',
  display: 'percent dose',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentEosSeen: Coding = {
  code: '%{EosSeen}',
  display: 'percent EosSeen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentExcretion: Coding = {
  code: '%{excretion}',
  display: 'percent excretion',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentExcretion_2: Coding = {
  code: '%{Excretion}',
  display: 'percent Excretion',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentFat: Coding = {
  code: '%{Fat}',
  display: 'percent Fat',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentFetalErythrocytes: Coding = {
  code: '%{FetalErythrocytes}',
  display: 'percent FetalErythrocytes',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentHemoglobin: Coding = {
  code: '%{Hb}',
  display: 'percent hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentHemoglobin_2: Coding = {
  code: '%{Hemoglobin}',
  display: 'percent Hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentHemoglobinA1C: Coding = {
  code: '%{HemoglobinA1C}',
  display: 'percent HemoglobinA1C',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentHemoglobinSaturation: Coding = {
  code: '%{HemoglobinSaturation}',
  display: 'percent HemoglobinSaturation',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentHemolysis: Coding = {
  code: '%{hemolysis}',
  display: 'percent hemolysis',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentHemolysis_2: Coding = {
  code: '%{Hemolysis}',
  display: 'percent Hemolysis',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentHumanResponse: Coding = {
  code: '%{HumanResponse}',
  display: 'percent HumanResponse',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentIndex: Coding = {
  code: '%{index}',
  display: 'percent index',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentIndex_2: Coding = {
  code: '%{Index}',
  display: 'percent Index',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentInhibition: Coding = {
  code: '%{inhibition}',
  display: 'percent inhibition',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentInhibition_2: Coding = {
  code: '%{Inhibition}',
  display: 'percent Inhibition',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentLive: Coding = {
  code: '%{Live}',
  display: 'percent Live',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentLossOfAcetylcholineReceptor: Coding = {
  code: '%{loss_AChR}',
  display: 'percent  loss of acetylcholine receptor',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentLoss: Coding = {
  code: '%{loss}',
  display: 'percent loss',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentLysis: Coding = {
  code: '%{lysis}',
  display: 'percent lysis',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentNegativeControl: Coding = {
  code: '%{Negative Control}',
  display: 'percent Negative Control',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentNormalPooledPlasma: Coding = {
  code: '%{normal_pooled_plasma}',
  display: 'percent normal pooled plasma',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentNormal: Coding = {
  code: '%{normal}',
  display: 'percent normal',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentNormal_2: Coding = {
  code: '%{Normal}',
  display: 'percent Normal',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentNormalControl: Coding = {
  code: '%{NormalControl}',
  display: 'percent NormalControl',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentNormalPooledPlasma_2: Coding = {
  code: '%{NormalPooledPlasma}',
  display: 'percent NormalPooledPlasma',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentOfAvailable: Coding = {
  code: '%{ofAvailable}',
  display: 'percent ofAvailable',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentOfBacteria_2: Coding = {
  code: '%{ofBacteria}',
  display: 'percent ofBacteria',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentOfLymphocytes: Coding = {
  code: '%{OfLymphocytes}',
  display: 'percent OfLymphocytes',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentOfWBCs: Coding = {
  code: '%{OfWBCs}',
  display: 'percent OfWBCs',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentOxygen: Coding = {
  code: '%{Oxygen}',
  display: 'percent Oxygen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentPenetration: Coding = {
  code: '%{penetration}',
  display: 'percent  penetration',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentPositive: Coding = {
  code: '%{positive}',
  display: 'percent positive',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentPositive_2: Coding = {
  code: '%{Positive}',
  display: 'percent Positive',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentPrecipitate: Coding = {
  code: '%{Precipitate}',
  display: 'percent Precipitate',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentOfRedBloodCells: Coding = {
  code: '%{RBCs}',
  display: 'percent of red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentReactive: Coding = {
  code: '%{reactive}',
  display: 'percent reactive',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentReactivity: Coding = {
  code: '%{Reactivity}',
  display: 'percent Reactivity',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentRecovery: Coding = {
  code: '%{recovery}',
  display: 'percent recovery',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentReference: Coding = {
  code: '%{reference}',
  display: 'percent reference',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RelativePercent: Coding = {
  code: '%{relative}',
  display: 'relative percent',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentResidual: Coding = {
  code: '%{residual}',
  display: 'percent residual',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentResponse: Coding = {
  code: '%{response}',
  display: 'percent response',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentRisk: Coding = {
  code: '%{risk}',
  display: 'percent risk',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentSaturation: Coding = {
  code: '%{saturation}',
  display: 'percent saturation',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentSpermMotility: Coding = {
  code: '%{SpermMotility}',
  display: 'percent SpermMotility',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentTotal: Coding = {
  code: '%{total}',
  display: 'percent total',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentTotal_2: Coding = {
  code: '%{Total}',
  display: 'percent Total',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentTotalProtein: Coding = {
  code: '%{TotalProtein}',
  display: 'percent TotalProtein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentTotQuoteCholesterol: Coding = {
  code: "%{Tot'Cholesterol}",
  display: "percent Tot'Cholesterol",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentTotQuoteHgb: Coding = {
  code: "%{Tot'Hgb}",
  display: "percent Tot'Hgb",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentUptake: Coding = {
  code: '%{uptake}',
  display: 'percent uptake',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentUptake_2: Coding = {
  code: '%{Uptake}',
  display: 'percent Uptake',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentViable: Coding = {
  code: '%{viable}',
  display: 'percent viable',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VolumePercent: Coding = {
  code: '%{vol}',
  display: 'VolumePercent',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentOfWhiteBloodCells: Coding = {
  code: '%{WBCs}',
  display: 'percent of white blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PercentWeightToWeight: Coding = {
  code: '%{WeightToWeight}',
  display: 'percent WeightToWeight',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Per12AsteriskHour: Coding = {
  code: '/(12.h)',
  display: 'per 12 * hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerArbitraryUnit: Coding = {
  code: "/[arb'U]",
  display: 'per arbitrary unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerHighPowerField: Coding = {
  code: '/[HPF]',
  display: 'per high power field',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerInternationalUnit: Coding = {
  code: '/[iU]',
  display: 'per international unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerInternationalUnit_2: Coding = {
  code: '/[IU]',
  display: 'per international unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerLowPowerField: Coding = {
  code: '/[LPF]',
  display: 'per low power field',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerEntity: Coding = {
  code: '/{entity}',
  display: 'per entity',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerEntity_2: Coding = {
  code: '/{Entity}',
  display: 'per Entity',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerOif: Coding = {
  code: '/{oif}',
  display: 'per oif',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerOilImmersionField: Coding = {
  code: '/{OIF}',
  display: 'per oil immersion field',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerSpecimen: Coding = {
  code: '/{Specimen}',
  display: 'per Specimen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerTot: Coding = {
  code: '/{tot}',
  display: 'per tot',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerTenGiga: Coding = {
  code: '/10*10',
  display: 'PerTenGiga',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerTrillion: Coding = {
  code: '/10*12',
  display: 'PerTrillion',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerTrillionRedBloodCells: Coding = {
  code: '/10*12{rbc}',
  display: 'PerTrillionRedBloodCells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerTrillionRedBloodCells_2: Coding = {
  code: '/10*12{RBCs}',
  display: 'per trillion red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerThousand: Coding = {
  code: '/10*3',
  display: 'per thousand',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerThousandRedBloodCells: Coding = {
  code: '/10*3.{RBCs}',
  display: 'per thousand red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Per10ThousandRedBloodCells: Coding = {
  code: '/10*4{RBCs}',
  display: 'per 10 thousand red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerMillion: Coding = {
  code: '/10*6',
  display: 'PerMillion',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerBillion: Coding = {
  code: '/10*9',
  display: 'PerBillion',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Per100: Coding = {
  code: '/100',
  display: 'per 100',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Per100Cells: Coding = {
  code: '/100{cells}',
  display: 'per 100 cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Per100Neutrophils: Coding = {
  code: '/100{neutrophils}',
  display: 'per 100 neutrophils',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Per100Spermatozoa: Coding = {
  code: '/100{spermatozoa}',
  display: 'per 100 spermatozoa',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Per100Spermatozoa_2: Coding = {
  code: '/100{Spermatozoa}',
  display: 'Per100Spermatozoa',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Per100WBC: Coding = {
  code: '/100{WBC}',
  display: 'Per100WBC',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Per100WBC_2: Coding = {
  code: '/100{WBCs}',
  display: 'Per100WBC',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerYear: Coding = {
  code: '/a',
  display: '/ year',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerCentimeterOfWater: Coding = {
  code: '/cm[H2O]',
  display: 'per centimeter of water',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerDay: Coding = {
  code: '/d',
  display: 'per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerDeciliter: Coding = {
  code: '/dL',
  display: 'per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerGram: Coding = {
  code: '/g',
  display: 'per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerGramCreat: Coding = {
  code: '/g{creat}',
  display: 'per gram creat',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerGramOfHemoglobin: Coding = {
  code: '/g{Hb}',
  display: 'per gram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerGramHgb: Coding = {
  code: '/g{hgb}',
  display: 'per gram hgb',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerGramHemoglobin: Coding = {
  code: '/g{HGB}',
  display: 'PerGramHemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerGramOfTotalNitrogen: Coding = {
  code: '/g{tot_nit}',
  display: 'per gram of total nitrogen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerGramOfTotalProtein: Coding = {
  code: '/g{tot_prot}',
  display: 'per gram of total protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerGramTotQuoteNit: Coding = {
  code: "/g{tot'nit}",
  display: "per gram tot'nit",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerGramTotQuoteProt: Coding = {
  code: "/g{tot'prot}",
  display: "per gram tot'prot",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerGramOfWetTissue: Coding = {
  code: '/g{wet_tis}',
  display: 'per gram of wet tissue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerGramWetQuoteTis: Coding = {
  code: "/g{wet'tis}",
  display: "per gram wet'tis",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerHour: Coding = {
  code: '/h',
  display: 'per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerKilogram: Coding = {
  code: '/kg',
  display: 'per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerKilogramOfBodyWeight: Coding = {
  code: '/kg{body_wt}',
  display: 'per kilogram of body weight',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerKilogramBodyWt: Coding = {
  code: "/kg{body'wt}",
  display: 'per kilogram body wt',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerLiter: Coding = {
  code: '/L',
  display: 'per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerSquareMeter: Coding = {
  code: '/m2',
  display: 'per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerCubicMeter: Coding = {
  code: '/m3',
  display: 'per cubic meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerMilligram: Coding = {
  code: '/mg',
  display: 'per milligram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerMinute: Coding = {
  code: '/min',
  display: 'per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerMilliliter: Coding = {
  code: '/mL',
  display: 'per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerMillimeter: Coding = {
  code: '/mm',
  display: 'per millimeter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerCubicMillimeter: Coding = {
  code: '/mm3',
  display: 'per cubic millimeter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerMillimole: Coding = {
  code: '/mmol',
  display: 'per millimole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerMillimoleOfCreatinine: Coding = {
  code: '/mmol{creat}',
  display: 'per millimole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerMonth: Coding = {
  code: '/mo',
  display: 'per month',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerSecond: Coding = {
  code: '/s',
  display: 'per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerEnzymeUnit: Coding = {
  code: '/U',
  display: 'per enzyme unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerMicrogram: Coding = {
  code: '/ug',
  display: 'per microgram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerMicroliter: Coding = {
  code: '/uL',
  display: 'per microliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PerWeek: Coding = {
  code: '/wk',
  display: 'per week',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Acre: Coding = {
  code: '[acr_br]',
  display: 'acre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Acre_2: Coding = {
  code: '[acr_us]',
  display: 'acre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_AmbA1Units: Coding = {
  code: "[Amb'a'1'U]",
  display: 'Amb a 1 units',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgAAnticardiolipinUnit: Coding = {
  code: "[APL'U]",
  display: 'IgA anticardiolipin unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgAAnticardiolipinUnitPerMilliliter: Coding = {
  code: "[APL'U]/mL",
  display: 'IgA anticardiolipin unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ArbitraryUnit: Coding = {
  code: "[arb'U]",
  display: 'arbitrary unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ArbitaryUnitLiter: Coding = {
  code: "[arb'U]/L",
  display: 'arbitary unit / liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ArbitraryUnitPerMilliliter: Coding = {
  code: "[arb'U]/mL",
  display: 'arbitrary unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_AllergyUnit: Coding = {
  code: '[AU]',
  display: 'allergy unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BioequivalentAllergenUnit: Coding = {
  code: '[BAU]',
  display: 'bioequivalent allergen unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Barrel: Coding = {
  code: '[bbl_us]',
  display: 'barrel',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BodanskyUnit: Coding = {
  code: "[bdsk'U]",
  display: 'Bodansky unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BethesdaUnit: Coding = {
  code: "[beth'U]",
  display: 'Bethesda unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BoardFoot: Coding = {
  code: '[bf_i]',
  display: 'board foot',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BritishThermalUnit: Coding = {
  code: '[Btu]',
  display: 'British thermal unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BritishThermalUnitAt39DegreesF: Coding = {
  code: '[Btu_39]',
  display: 'British thermal unit at 39 °F',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BritishThermalUnitAt59DegreesF: Coding = {
  code: '[Btu_59]',
  display: 'British thermal unit at 59 °F',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BritishThermalUnitAt60DegreesF: Coding = {
  code: '[Btu_60]',
  display: 'British thermal unit at 60 °F',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalTableBritishThermalUnit: Coding = {
  code: '[Btu_IT]',
  display: 'international table British thermal unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MeanBritishThermalUnit: Coding = {
  code: '[Btu_m]',
  display: 'mean British thermal unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ThermochemicalBritishThermalUnit: Coding = {
  code: '[Btu_th]',
  display: 'thermochemical British thermal unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Bushel: Coding = {
  code: '[bu_br]',
  display: 'bushel',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Bushel_2: Coding = {
  code: '[bu_us]',
  display: 'bushel',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VelocityOfLight: Coding = {
  code: '[c]',
  display: 'velocity of light',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NutritionLabelCalories: Coding = {
  code: '[Cal]',
  display: 'nutrition label Calories',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CaratOfGoldAlloys: Coding = {
  code: '[car_Au]',
  display: 'carat of gold alloys',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MetricCarat: Coding = {
  code: '[car_m]',
  display: 'metric carat',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CELLCULTUREINFECTIOUSDOSE50Percent: Coding = {
  code: '[CCID_50]',
  display: 'CELL CULTURE INFECTIOUS DOSE 50%',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CubicFoot: Coding = {
  code: '[cft_i]',
  display: 'cubic foot',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ColonyFormingUnit: Coding = {
  code: '[CFU]',
  display: 'colony forming unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ColonyFormingUnitPerLiter: Coding = {
  code: '[CFU]/L',
  display: 'colony forming unit per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ColonyFormingUnitPerMilliliter: Coding = {
  code: '[CFU]/mL',
  display: 'colony forming unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FrenchCatheterGauge: Coding = {
  code: '[Ch]',
  display: 'French (catheter gauge)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GunterQuoteSChain: Coding = {
  code: '[ch_br]',
  display: "Gunter's chain",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GunterQuoteSChainSurveyorQuoteSChain: Coding = {
  code: '[ch_us]',
  display: "Gunter's chain Surveyor's chain",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CiceroDidotQuoteSPica: Coding = {
  code: '[cicero]',
  display: "cicero Didot's pica",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CubicInch: Coding = {
  code: '[cin_i]',
  display: 'cubic inch',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CircularMil: Coding = {
  code: '[cml_i]',
  display: 'circular mil',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Cord: Coding = {
  code: '[cr_i]',
  display: 'cord',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Cord_2: Coding = {
  code: '[crd_us]',
  display: 'cord',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Cup: Coding = {
  code: '[cup_us]',
  display: 'cup',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CubicYard: Coding = {
  code: '[cyd_i]',
  display: 'cubic yard',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DANTIGENUNITS: Coding = {
  code: "[D'ag'U]",
  display: 'D-ANTIGEN UNITS',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DidotDidotQuoteSPoint: Coding = {
  code: '[didot]',
  display: "didot Didot's point",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Diopter: Coding = {
  code: '[diop]',
  display: 'diopter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DryPint: Coding = {
  code: '[dpt_us]',
  display: 'dry pint',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DryQuart: Coding = {
  code: '[dqt_us]',
  display: 'dry quart',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DramDrachm: Coding = {
  code: '[dr_ap]',
  display: 'dram drachm',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DramUSAndBritish: Coding = {
  code: '[dr_av]',
  display: 'Dram (US and British)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Drop: Coding = {
  code: '[drp]',
  display: 'drop',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DropHPF: Coding = {
  code: '[drp]/[HPF]',
  display: 'drop / HPF',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DropHour: Coding = {
  code: '[drp]/h',
  display: 'drop / hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DropMinute: Coding = {
  code: '[drp]/min',
  display: 'drop / minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DropMilliliter: Coding = {
  code: '[drp]/mL',
  display: 'drop / milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DropSecond: Coding = {
  code: '[drp]/s',
  display: 'drop / second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DyeUnit: Coding = {
  code: "[dye'U]",
  display: 'Dye unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ElementaryCharge: Coding = {
  code: '[e]',
  display: 'elementary charge',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PermittivityOfVacuum: Coding = {
  code: '[eps_0]',
  display: 'permittivity of vacuum',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FluidDram: Coding = {
  code: '[fdr_br]',
  display: 'fluid dram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FluidDram_2: Coding = {
  code: '[fdr_us]',
  display: 'fluid dram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FOCUSFORMINGUNITS: Coding = {
  code: '[FFU]',
  display: 'FOCUS-FORMING UNITS',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FluidOunce: Coding = {
  code: '[foz_br]',
  display: 'fluid ounce',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FluidOunce_2: Coding = {
  code: '[foz_us]',
  display: 'fluid ounce',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Foot: Coding = {
  code: '[ft_br]',
  display: 'foot',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Feet: Coding = {
  code: '[ft_i]',
  display: 'Feet',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Foot_2: Coding = {
  code: '[ft_us]',
  display: 'foot',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Fathom: Coding = {
  code: '[fth_br]',
  display: 'fathom',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Fathom_2: Coding = {
  code: '[fth_i]',
  display: 'fathom',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Fathom_3: Coding = {
  code: '[fth_us]',
  display: 'fathom',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Furlong: Coding = {
  code: '[fur_us]',
  display: 'furlong',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NewtonianConstantOfGravitation: Coding = {
  code: '[G]',
  display: 'Newtonian constant of gravitation',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Gallon: Coding = {
  code: '[gal_br]',
  display: 'gallon',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_QueenAnneQuoteSWineGallon: Coding = {
  code: '[gal_us]',
  display: "Queen Anne's wine gallon",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_HistoricalWinchesterGallon: Coding = {
  code: '[gal_wi]',
  display: 'historical winchester gallon',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Gill: Coding = {
  code: '[gil_br]',
  display: 'gill',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Gill_2: Coding = {
  code: '[gil_us]',
  display: 'gill',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgGAnticardiolipinUnit: Coding = {
  code: "[GPL'U]",
  display: 'IgG anticardiolipin unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgGAnticardiolipinUnitPerMilliliterAsteriskAsterisk: Coding = {
  code: "[GPL'U]/mL",
  display: 'IgG anticardiolipin unit per milliliter**',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Grain: Coding = {
  code: '[gr]',
  display: 'grain',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PlanckConstant: Coding = {
  code: '[h]',
  display: 'Planck constant',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Hand: Coding = {
  code: '[hd_i]',
  display: 'hand',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_HounsfieldUnit: Coding = {
  code: "[hnsf'U]",
  display: 'Hounsfield unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Horsepower: Coding = {
  code: '[HP]',
  display: 'horsepower',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_HOMEOPATHICPOTENCYOFCENTESIMALSERIES: Coding = {
  code: '[hp_C]',
  display: 'HOMEOPATHIC POTENCY OF CENTESIMAL SERIES',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_HOMEOPATHICPOTENCYOFMILLESIMALSERIES: Coding = {
  code: '[hp_M]',
  display: 'HOMEOPATHIC POTENCY OF MILLESIMAL SERIES',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_HOMEOPATHICPOTENCYOFQUINTAMILLESIMALSERIES: Coding = {
  code: '[hp_Q]',
  display: 'HOMEOPATHIC POTENCY OF QUINTAMILLESIMAL SERIES',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_HOMEOPATHICPOTENCYOFDECIMALSERIES: Coding = {
  code: '[hp_X]',
  display: 'HOMEOPATHIC POTENCY OF DECIMAL SERIES',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_HighPowerField: Coding = {
  code: '[HPF]',
  display: 'high power field',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Inch: Coding = {
  code: '[in_br]',
  display: 'inch',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InchInternationalOfWater: Coding = {
  code: "[in_i'H2O]",
  display: 'inch (international) of water',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InchOfMercuryColumn: Coding = {
  code: "[in_i'Hg]",
  display: 'inch of mercury column',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Inch_2: Coding = {
  code: '[in_us]',
  display: 'inch',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnit: Coding = {
  code: '[iU]',
  display: 'international unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnit_2: Coding = {
  code: '[IU]',
  display: 'international unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPer2Hour: Coding = {
  code: '[IU]/(2.h)',
  display: 'international unit per 2 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPer24Hour: Coding = {
  code: '[IU]/(24.h)',
  display: 'international unit per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPerBillionRedBloodCells: Coding = {
  code: '[IU]/10*9{RBCs}',
  display: 'international unit per billion red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPerDay: Coding = {
  code: '[IU]/d',
  display: 'international unit per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitsPerDeciLiter: Coding = {
  code: '[iU]/dL',
  display: 'InternationalUnitsPerDeciLiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPerDeciliter: Coding = {
  code: '[IU]/dL',
  display: 'international unit per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitsPerGram: Coding = {
  code: '[iU]/g',
  display: 'InternationalUnitsPerGram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPerGram: Coding = {
  code: '[IU]/g',
  display: 'international unit per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPerGramOfHemoglobin: Coding = {
  code: '[IU]/g{Hb}',
  display: 'international unit per gram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitGramHgb: Coding = {
  code: '[iU]/g{Hgb}',
  display: 'international unit / gram Hgb',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPerHour: Coding = {
  code: '[IU]/h',
  display: 'international unit per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitsPerKilogram: Coding = {
  code: '[iU]/kg',
  display: 'InternationalUnitsPerKilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPerKilogram: Coding = {
  code: '[IU]/kg',
  display: 'international unit per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPerKilogramPerDay: Coding = {
  code: '[IU]/kg/d',
  display: 'international unit per kilogram per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitsPerLiter: Coding = {
  code: '[iU]/L',
  display: 'InternationalUnitsPerLiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPerLiter: Coding = {
  code: '[IU]/L',
  display: 'international unit per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPerLiterAt37DegreesCelsius: Coding = {
  code: '[IU]/L{37Cel}',
  display: 'international unit per liter at 37 degrees Celsius',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPerMilligramOfCreatinine: Coding = {
  code: '[IU]/mg{creat}',
  display: 'international unit per milligram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPerMinute: Coding = {
  code: '[IU]/min',
  display: 'international unit per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitsPerMilliLiter: Coding = {
  code: '[iU]/mL',
  display: 'InternationalUnitsPerMilliLiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalUnitPerMilliliter: Coding = {
  code: '[IU]/mL',
  display: 'international unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BoltzmannConstant: Coding = {
  code: '[k]',
  display: 'Boltzmann constant',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KingArmstrongUnit: Coding = {
  code: "[ka'U]",
  display: 'King Armstrong unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Knot: Coding = {
  code: '[kn_br]',
  display: 'knot',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Knot_2: Coding = {
  code: '[kn_i]',
  display: 'knot',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KunkelUnit: Coding = {
  code: "[knk'U]",
  display: 'Kunkel unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Pound: Coding = {
  code: '[lb_ap]',
  display: 'pound',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Pound_2: Coding = {
  code: '[lb_tr]',
  display: 'pound',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PoundForce: Coding = {
  code: '[lbf_av]',
  display: 'pound force',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LongHunderdweightBritishHundredweight: Coding = {
  code: '[lcwt_av]',
  display: 'long hunderdweight British hundredweight',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LIMITOFFLOCCULATION: Coding = {
  code: '[Lf]',
  display: 'LIMIT OF FLOCCULATION',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LigneFrenchLine: Coding = {
  code: '[ligne]',
  display: 'ligne French line',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LinkForGunterQuoteSChain: Coding = {
  code: '[lk_br]',
  display: "link for Gunter's chain",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LinkForGunterQuoteSChain_2: Coding = {
  code: '[lk_us]',
  display: "link for Gunter's chain",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Line: Coding = {
  code: '[lne]',
  display: 'line',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LowPowerField: Coding = {
  code: '[LPF]',
  display: 'low power field',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LongTonBritishTon: Coding = {
  code: '[lton_av]',
  display: 'long ton British ton',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LightYear: Coding = {
  code: '[ly]',
  display: 'light-year',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ElectronMass: Coding = {
  code: '[m_e]',
  display: 'electron mass',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ProtonMass: Coding = {
  code: '[m_p]',
  display: 'proton mass',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MacLaganUnit: Coding = {
  code: "[mclg'U]",
  display: 'Mac Lagan unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Mesh: Coding = {
  code: '[mesh_i]',
  display: 'mesh',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MetabolicEquivalent: Coding = {
  code: '[MET]',
  display: 'metabolic equivalent',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Mile: Coding = {
  code: '[mi_br]',
  display: 'mile',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_StatuteMile: Coding = {
  code: '[mi_i]',
  display: 'statute mile',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Mile_2: Coding = {
  code: '[mi_us]',
  display: 'mile',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Mil: Coding = {
  code: '[mil_i]',
  display: 'mil',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Mil_2: Coding = {
  code: '[mil_us]',
  display: 'mil',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Minim: Coding = {
  code: '[min_br]',
  display: 'minim',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Minim_2: Coding = {
  code: '[min_us]',
  display: 'minim',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgMAnticardiolipinUnit: Coding = {
  code: "[MPL'U]",
  display: 'IgM anticardiolipin unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgMAnticardiolipinUnitPerMilliliterAsteriskAsterisk: Coding = {
  code: "[MPL'U]/mL",
  display: 'IgM anticardiolipin unit per milliliter**',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PermeabilityOfVacuum: Coding = {
  code: '[mu_0]',
  display: 'permeability of vacuum',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NauticalMile: Coding = {
  code: '[nmi_br]',
  display: 'nautical mile',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NauticalMile_2: Coding = {
  code: '[nmi_i]',
  display: 'nautical mile',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_OunceUSAndBritish: Coding = {
  code: '[oz_ap]',
  display: 'ounce (US and British)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_OunceUSAndBritish_2: Coding = {
  code: '[oz_av]',
  display: 'ounce (US and British)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Ounce: Coding = {
  code: '[oz_tr]',
  display: 'ounce',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Pace: Coding = {
  code: '[pc_br]',
  display: 'pace',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Pica: Coding = {
  code: '[pca]',
  display: 'pica',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PrinterQuoteSPica: Coding = {
  code: '[pca_pr]',
  display: "Printer's pica",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PrismDiopter: Coding = {
  code: "[p'diop]",
  display: 'prism diopter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PLAQUEFORMINGUNITS: Coding = {
  code: '[PFU]',
  display: 'PLAQUE-FORMING UNITS',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PH: Coding = {
  code: '[pH]',
  display: 'pH',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TheNumberPi: Coding = {
  code: '[pi]',
  display: 'the number pi',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TheNumberPiAsteriskRadianMinute: Coding = {
  code: '[pi].rad/min',
  display: 'the number pi * radian / minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PiedFrenchFoot: Coding = {
  code: '[pied]',
  display: 'pied French foot',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Peck: Coding = {
  code: '[pk_br]',
  display: 'peck',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Peck_2: Coding = {
  code: '[pk_us]',
  display: 'peck',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Point: Coding = {
  code: '[pnt]',
  display: 'point',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PrinterQuoteSPoint: Coding = {
  code: '[pnt_pr]',
  display: "Printer's point",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PROTEINNITROGENUNITS: Coding = {
  code: '[PNU]',
  display: 'PROTEIN NITROGEN UNITS',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PouceFrenchInch: Coding = {
  code: '[pouce]',
  display: 'pouce French inch',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PartPerBillion: Coding = {
  code: '[ppb]',
  display: 'part per billion',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PartPerMillion: Coding = {
  code: '[ppm]',
  display: 'part per million',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PartPerMillionInVolumePerVolume: Coding = {
  code: '[ppm]{v/v}',
  display: 'part per million in volume per volume',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PartsPerThousand: Coding = {
  code: '[ppth]',
  display: 'parts per thousand',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PartPerTrillion: Coding = {
  code: '[pptr]',
  display: 'part per trillion',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PeripheralVascularResistanceUnit: Coding = {
  code: '[PRU]',
  display: 'peripheral vascular resistance unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PoundPerSquareInch: Coding = {
  code: '[psi]',
  display: 'pound per square inch',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Pint: Coding = {
  code: '[pt_br]',
  display: 'pint',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Pint_2: Coding = {
  code: '[pt_us]',
  display: 'pint',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Pennyweight: Coding = {
  code: '[pwt_tr]',
  display: 'pennyweight',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Quart: Coding = {
  code: '[qt_br]',
  display: 'quart',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Quart_2: Coding = {
  code: '[qt_us]',
  display: 'quart',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RamdenQuoteSChainEngineerQuoteSChain: Coding = {
  code: '[rch_us]',
  display: "Ramden's chain Engineer's chain",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Rod: Coding = {
  code: '[rd_br]',
  display: 'rod',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Rod_2: Coding = {
  code: '[rd_us]',
  display: 'rod',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LinkForRamdenQuoteSChain: Coding = {
  code: '[rlk_us]',
  display: "link for Ramden's chain",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SvedbergUnit: Coding = {
  code: '[S]',
  display: 'Svedberg unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Scruple: Coding = {
  code: '[sc_ap]',
  display: 'scruple',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Section: Coding = {
  code: '[sct]',
  display: 'section',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ShortHundredweightUSHundredweight: Coding = {
  code: '[scwt_av]',
  display: 'short hundredweight U.S. hundredweight',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SquareFootInternational: Coding = {
  code: '[sft_i]',
  display: 'square foot (international)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SquareInchInternational: Coding = {
  code: '[sin_i]',
  display: 'square inch (international)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SomogyiUnit: Coding = {
  code: "[smgy'U]",
  display: 'Somogyi unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SquareMile: Coding = {
  code: '[smi_us]',
  display: 'square mile',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Smoot: Coding = {
  code: '[smoot]',
  display: 'Smoot',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SquareRod: Coding = {
  code: '[srd_us]',
  display: 'square rod',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ShortTonUSTon: Coding = {
  code: '[ston_av]',
  display: 'short ton U.S. ton',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_StoneBritishStone: Coding = {
  code: '[stone_av]',
  display: 'stone British stone',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SquareYard: Coding = {
  code: '[syd_i]',
  display: 'square yard',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TablespoonUS: Coding = {
  code: '[tbs_us]',
  display: 'tablespoon (US)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TuberculinUnit: Coding = {
  code: "[tb'U]",
  display: 'tuberculin unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TISSUECULTUREINFECTIOUSDOSE50Percent: Coding = {
  code: '[TCID_50]',
  display: 'TISSUE CULTURE INFECTIOUS DOSE 50%',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ToddUnit: Coding = {
  code: "[todd'U]",
  display: 'Todd unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Teaspoon: Coding = {
  code: '[tsp_us]',
  display: 'teaspoon',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Township: Coding = {
  code: '[twp]',
  display: 'township',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_UNITEDSTATESPHARMACOPEIAUNIT: Coding = {
  code: "[USP'U]",
  display: 'UNITED STATES PHARMACOPEIA UNIT',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Yard: Coding = {
  code: '[yd_br]',
  display: 'yard',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Yard_2: Coding = {
  code: '[yd_i]',
  display: 'yard',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Yard_3: Coding = {
  code: '[yd_us]',
  display: 'yard',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NumberOfCalculi: Coding = {
  code: '{# of calculi}',
  display: '# of calculi',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NumberOfDonorInformativeMarkers: Coding = {
  code: '{# of donor informative markers}',
  display: '# of donor informative markers',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NumberOfFetuses: Coding = {
  code: '{# of fetuses}',
  display: '# of fetuses',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NumberOfInformativeMarkers: Coding = {
  code: '{# of informative markers}',
  display: '# of informative markers',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Number: Coding = {
  code: '{#}',
  display: '#',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NumberPerHighPowerField: Coding = {
  code: '{#}/[HPF]',
  display: 'number per high power field',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NumberPerLowPowerField: Coding = {
  code: '{#}/[LPF]',
  display: 'number per low power field',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NumberPerLiter: Coding = {
  code: '{#}/L',
  display: 'number per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NumberPerMinute: Coding = {
  code: '{#}/min',
  display: 'number per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NumberPerMilliliter: Coding = {
  code: '{#}/mL',
  display: 'number per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NumberPerMicroliter: Coding = {
  code: '{#}/uL',
  display: 'number per microliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VAL2Or3TimesDay: Coding = {
  code: '{2 or 3 times}/d',
  display: '2 or 3 times / day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VAL3TimesDay: Coding = {
  code: '{3 times}/d',
  display: '3 times / day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VAL4TimesDay: Coding = {
  code: '{4 times}/d',
  display: '4 times / day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VAL5TimesDay: Coding = {
  code: '{5 times}/d',
  display: '5 times / day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Absorbance: Coding = {
  code: '{absorbance}',
  display: 'absorbance',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_AbsorbanceQuoteU: Coding = {
  code: "{Absorbance'U}",
  display: "Absorbance'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_AbsorbanceQuoteUMilliliter: Coding = {
  code: "{Absorbance'U}/mL",
  display: "Absorbance'U / milliliter",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Activity: Coding = {
  code: '{activity}',
  display: 'activity',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ActivityCoefficient: Coding = {
  code: '{ActivityCoefficient}',
  display: 'ActivityCoefficient',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_AHFQuoteU: Coding = {
  code: "{AHF'U}",
  display: "AHF'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_AntibodyResponseQuoteU: Coding = {
  code: "{AntibodyResponse'U}",
  display: "AntibodyResponse'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Applicator: Coding = {
  code: '{Applicator}',
  display: 'Applicator',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgAAntiphosphatidylserineUnit: Coding = {
  code: "{APS'U}",
  display: 'IgA antiphosphatidylserine unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_AspirinResponseUnit: Coding = {
  code: '{ARU}',
  display: 'aspirin response unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_AspirinReactionQuoteU: Coding = {
  code: "{AspirinReaction'U}",
  display: "AspirinReaction'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Bead: Coding = {
  code: '{Bead}',
  display: 'Bead',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_HeartBeatsPerMinute: Coding = {
  code: '{beats}/min',
  display: 'heart beats per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BeatsMinute: Coding = {
  code: '{Beats}/min',
  display: 'Beats / minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BindingIndex: Coding = {
  code: '{binding_index}',
  display: 'binding index',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Bottle: Coding = {
  code: '{Bottle}',
  display: 'Bottle',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BowlsDay: Coding = {
  code: '{Bowls}/d',
  display: 'Bowls / day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BreathsMinute: Coding = {
  code: '{Breaths}/min',
  display: 'Breaths / minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ComplementActivityEnzymeUnit: Coding = {
  code: "{CAE'U}",
  display: 'complement activity enzyme unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CAGTrinucleotideRepeats: Coding = {
  code: '{CAG_repeats}',
  display: 'CAG trinucleotide repeats',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CagRepeat: Coding = {
  code: '{CagRepeat}',
  display: 'CagRepeat',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Can: Coding = {
  code: '{Can}',
  display: 'Can',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CansWeek: Coding = {
  code: '{Cans}/wk',
  display: 'Cans / week',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Capsule: Coding = {
  code: '{Capsule}',
  display: 'Capsule',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Cell: Coding = {
  code: '{Cell}',
  display: 'Cell',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Cells: Coding = {
  code: '{cells}',
  display: 'cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CellsPerHighPowerField: Coding = {
  code: '{cells}/[HPF]',
  display: 'cells per high power field',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CellsMilliliter: Coding = {
  code: '{Cells}/mL',
  display: 'Cells / milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CellsPerMicroliter: Coding = {
  code: '{cells}/uL',
  display: 'cells per microliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CellsPerMicroLiter: Coding = {
  code: '{Cells}/uL',
  display: 'CellsPerMicroLiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CfTiter: Coding = {
  code: '{CfTiter}',
  display: 'CfTiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Cfu: Coding = {
  code: '{cfu}',
  display: 'cfu',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CfuMilliliter: Coding = {
  code: '{cfu}/mL',
  display: 'cfu / milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CGGRepeats: Coding = {
  code: '{CGG_repeats}',
  display: 'CGG_repeats',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CGG: Coding = {
  code: '{CGG}',
  display: 'CGG',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ComplementCH100Unit: Coding = {
  code: "{CH100'U}",
  display: 'complement CH100 unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ClockTime: Coding = {
  code: '{clock time}',
  display: 'clock time',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ClockTime_2: Coding = {
  code: '{clock_time}',
  display: 'clock_time',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ComplementActivityEnzymeQuoteU: Coding = {
  code: "{ComplementActivityEnzyme'U}",
  display: "ComplementActivityEnzyme'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ComplementCH100QuoteU: Coding = {
  code: "{ComplementCH100'U}",
  display: "ComplementCH100'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ComplementCH50QuoteU: Coding = {
  code: "{ComplementCH50'U}",
  display: "ComplementCH50'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Copies: Coding = {
  code: '{copies}',
  display: 'copies',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CopiesPerMilliliter: Coding = {
  code: '{copies}/mL',
  display: 'copies per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CopiesPerMilliLiter: Coding = {
  code: '{Copies}/mL',
  display: 'CopiesPerMilliLiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CopiesPerMicrogram: Coding = {
  code: '{copies}/ug',
  display: 'copies per microgram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CopiesMicroliter: Coding = {
  code: '{Copies}/uL',
  display: 'Copies / microliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Count: Coding = {
  code: '{count}',
  display: 'count',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Count_2: Coding = {
  code: '{Count}',
  display: 'Count',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CountsMinute: Coding = {
  code: '{Counts}/min',
  display: 'Counts / minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CountsPerMinute: Coding = {
  code: '{CPM}',
  display: 'counts per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CountsPerMinutePerThousandCells: Coding = {
  code: '{CPM}/10*3{cell}',
  display: 'counts per minute per thousand cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CrossingThreshold: Coding = {
  code: '{Ct}',
  display: 'crossing threshold',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Dalton: Coding = {
  code: '{Dalton}',
  display: 'Dalton',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DdTiter: Coding = {
  code: '{DdTiter}',
  display: 'DdTiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ChangeInDeltaOpticalDensity: Coding = {
  code: '{delta_OD}',
  display: 'change in (delta) optical density',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DeltaOpticalDensity: Coding = {
  code: '{DeltaOpticalDensity}',
  display: 'DeltaOpticalDensity',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Dilution: Coding = {
  code: '{dilution}',
  display: 'dilution',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Dilution_2: Coding = {
  code: '{Dilution}',
  display: 'dilution',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DisintegrationsMinute: Coding = {
  code: '{Disintegrations}/min',
  display: 'Disintegrations / minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Dose: Coding = {
  code: '{Dose}',
  display: 'Dose',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DrinksDay: Coding = {
  code: '{Drinks}/d',
  display: 'Drinks / day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Each: Coding = {
  code: '{Each}',
  display: 'Each',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EhrlichUnitsPerDeciLiter: Coding = {
  code: '{Ehrlich_U}/dL',
  display: 'EhrlichUnitsPerDeciLiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EhrlichUnit: Coding = {
  code: "{Ehrlich'U}",
  display: 'Ehrlich unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EhrlichUnitPer2Hour: Coding = {
  code: "{Ehrlich'U}/(2.h)",
  display: 'Ehrlich unit per 2 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EhrlichUnitPer100Gram: Coding = {
  code: "{Ehrlich'U}/100.g",
  display: 'Ehrlich unit per 100 gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EhrlichUnitPerDay: Coding = {
  code: "{Ehrlich'U}/d",
  display: 'Ehrlich unit per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EhrlichUnitsPerDeciLiterArbitraryConcentrationUnits: Coding = {
  code: '{EhrlichU}/dL',
  display: 'EhrlichUnitsPerDeciLiter [Arbitrary Concentration Units]',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EhrilichUnitPerDeciliter: Coding = {
  code: "{Ehrlich'U}/dL",
  display: 'Ehrilich unit per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EhrlichQuoteUMilliliter: Coding = {
  code: "{Ehrlich'U}/mL",
  display: "Ehrlich'U / milliliter",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EIAIndex: Coding = {
  code: '{EIA_index}',
  display: 'EIA index',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EIATiter: Coding = {
  code: '{EIA_titer}',
  display: 'EIA titer',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EIAIndex_2: Coding = {
  code: '{EIAIndex}',
  display: 'EIA index',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EIATiter_2: Coding = {
  code: '{EIATiter}',
  display: 'EIA titer',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EIAUnit: Coding = {
  code: "{EIA'U}",
  display: 'EIA unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EIAUnitPerEnzymeUnit: Coding = {
  code: "{EIA'U}/U",
  display: 'EIA unit per enzyme Unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ElisaUnitsPerMilliLiter: Coding = {
  code: '{Elisa_U}/mL',
  display: 'ElisaUnitsPerMilliLiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ElisaIndex: Coding = {
  code: '{ElisaIndex}',
  display: 'ElisaIndex',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ELISAUnit: Coding = {
  code: "{ELISA'U}",
  display: 'ELISA unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ElisaUnitsPerMilliLiterArbitraryConcentrationUnits: Coding = {
  code: '{ElisaU}/mL',
  display: 'ElisaUnitsPerMilliLiter [Arbitrary Concentration Units]',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ElisaQuoteUMilliliter: Coding = {
  code: "{Elisa'U}/mL",
  display: "Elisa'U / milliliter",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ElisaValue: Coding = {
  code: '{ElisaValue}',
  display: 'ElisaValue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ErythrocytePerMicroliter: Coding = {
  code: '{ERY}/uL',
  display: 'erythrocyte per microliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EIAValue: Coding = {
  code: '{EV}',
  display: 'EIA value',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Events: Coding = {
  code: '{Events}',
  display: 'Events',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FluorescentIntensityUnit: Coding = {
  code: '{FIU}',
  display: 'fluorescent intensity unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FluorescenceIntensityQuoteU: Coding = {
  code: "{FluorescenceIntensity'U}",
  display: "FluorescenceIntensity'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Fraction: Coding = {
  code: '{fraction}',
  display: 'fraction',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GAATrinucleotideRepeats: Coding = {
  code: '{GAA_repeats}',
  display: 'GAA trinucleotide repeats',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GenomesPerMilliliter: Coding = {
  code: '{genomes}/mL',
  display: 'genomes per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GliadinIndexValue: Coding = {
  code: '{GliadinIndexValue}',
  display: 'GliadinIndexValue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GlobulesDropsPerHighPowerField: Coding = {
  code: '{Globules}/[HPF]',
  display: 'globules (drops)  per high power field',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GPortionPhospholipids: Coding = {
  code: '{G-PortionPhospholipids}',
  display: 'G-PortionPhospholipids',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgGAntiphosphatidylserineUnit: Coding = {
  code: "{GPS'U}",
  display: 'IgG antiphosphatidylserine unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InfluenzaHemagglutinationTiter: Coding = {
  code: '{HA_titer}',
  display: 'influenza hemagglutination titer',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_HaTiter: Coding = {
  code: '{HaTiter}',
  display: 'HaTiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ImmunofluorescenceAssayIndex: Coding = {
  code: '{IFA_index}',
  display: 'immunofluorescence assay index',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ImmunofluorescenceAssayTiter: Coding = {
  code: '{IFA_titer}',
  display: 'Immunofluorescence assay titer',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IfaIndex: Coding = {
  code: '{IfaIndex}',
  display: 'IfaIndex',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IfaTiter: Coding = {
  code: '{IfaTiter}',
  display: 'IfaTiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgAAntiphosphatidyleserineQuoteU: Coding = {
  code: "{IgAAntiphosphatidyleserine'U}",
  display: "IgAAntiphosphatidyleserine'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgAPhospholipidQuoteU: Coding = {
  code: "{IgAPhospholipid'U}",
  display: "IgAPhospholipid'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgGAntiphosphatidyleserineQuoteU: Coding = {
  code: "{IgGAntiphosphatidyleserine'U}",
  display: "IgGAntiphosphatidyleserine'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgGIndex: Coding = {
  code: '{IgGIndex}',
  display: 'IgGIndex',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgMAntiphosphatidyleserineQuoteU: Coding = {
  code: "{IgMAntiphosphatidyleserine'U}",
  display: "IgMAntiphosphatidyleserine'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgMIndex: Coding = {
  code: '{IgMIndex}',
  display: 'IgMIndex',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ImmuneComplexUnit: Coding = {
  code: "{ImmuneComplex'U}",
  display: 'immune complex unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ImmuneStatusRatio: Coding = {
  code: '{ImmuneStatusRatio}',
  display: 'ImmuneStatusRatio',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Immunity: Coding = {
  code: '{Immunity}',
  display: 'Immunity',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IndexValue: Coding = {
  code: '{index_val}',
  display: 'index value',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IndexVal: Coding = {
  code: '{Index_val}',
  display: 'Index_val',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Index: Coding = {
  code: '{index}',
  display: 'index',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IndexValue_2: Coding = {
  code: '{IndexValue}',
  display: 'IndexValue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InhaledTobaccoUseAmountYears: Coding = {
  code: '{InhaledTobaccoUseAmountYears}',
  display: 'InhaledTobaccoUseAmountYears',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InhaledTobaccoUsePacksDay: Coding = {
  code: '{InhaledTobaccoUsePacks}/d',
  display: 'InhaledTobaccoUsePacks / day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalNormalizedRatio: Coding = {
  code: '{INR}',
  display: 'international normalized ratio',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_INRQuoteUnit: Coding = {
  code: "{INR'unit}",
  display: "INR'unit",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ImmuneStatusRatio_2: Coding = {
  code: '{ISR}',
  display: 'immune status ratio',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_JuvenileDiabetesFoundationUnit: Coding = {
  code: "{JDF'U}",
  display: 'Juvenile Diabetes Foundation unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_JuvenileDiabetesFoundationUnitPerLiter: Coding = {
  code: "{JDF'U}/L",
  display: 'Juvenile Diabetes Foundation unit per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_JuvenileDiabetesFoundQuoteU: Coding = {
  code: "{JuvenileDiabetesFound'U}",
  display: "JuvenileDiabetesFound'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KaolinClottingTime: Coding = {
  code: "{KCT'U}",
  display: 'kaolin clotting time',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_HOMEOPATHICPOTENCYOFCENTESIMALKORSAKOVIANSERIES: Coding = {
  code: '{kp_C}',
  display: 'HOMEOPATHIC POTENCY OF CENTESIMAL KORSAKOVIAN SERIES',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KronusUnitPerLiter: Coding = {
  code: "{KRONU'U}/L",
  display: 'Kronus unit per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KronusUnitPerMilliliter: Coding = {
  code: "{KRONU'U}/mL",
  display: 'Kronus unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LgCopiesMilliliter: Coding = {
  code: '{lgCopies}/ml',
  display: 'lgCopies / milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LogBase10CopiesPerMilliliter: Coding = {
  code: '{log_copies}/mL',
  display: 'log (base 10) copies per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LogBase10CopiesPerMilliliter_2: Coding = {
  code: '{Log_copies}/mL',
  display: 'log (base 10) copies per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LogBase10InternationalUnit: Coding = {
  code: '{Log_IU}',
  display: 'log (base 10) international unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LogBase10InternationalUnitPerMilliliter: Coding = {
  code: '{log_IU}/mL',
  display: 'log (base 10) international unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LogBase10InternationalUnitPerMilliliter_2: Coding = {
  code: '{Log_IU}/mL',
  display: 'log (base 10) international unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LogBase10: Coding = {
  code: '{Log}',
  display: 'log base 10',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LymeIndexValue: Coding = {
  code: '{Lyme_index_value}',
  display: 'Lyme index value',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LymeIndexValue_2: Coding = {
  code: '{LymeIndexValue}',
  display: 'LymeIndexValue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MOM: Coding = {
  code: '{M.o.M.}',
  display: 'M.o.M.',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MultipleOfTheMedian: Coding = {
  code: '{M.o.M}',
  display: 'multiple of the median',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Markers: Coding = {
  code: '{Markers}',
  display: 'Markers',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MinidropPerMinute: Coding = {
  code: '{minidrop}/min',
  display: 'minidrop per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MinidropPerSecond: Coding = {
  code: '{minidrop}/s',
  display: 'minidrop per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Minidrp: Coding = {
  code: '{minidrp}',
  display: 'minidrp',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MonthDayYear: Coding = {
  code: '{mm/dd/yyyy}',
  display: 'month-day-year',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MoleculePerPlatelet: Coding = {
  code: '{molecule}/{platelet}',
  display: 'molecule per platelet',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MoleculePlatelet: Coding = {
  code: '{Molecule}/{Platelet}',
  display: 'Molecule / Platelet',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MPortionPhospholipids: Coding = {
  code: '{M-PortionPhospholipids}',
  display: 'M-PortionPhospholipids',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgMAntiphosphatidylserineUnit: Coding = {
  code: "{MPS'U}",
  display: 'IgM antiphosphatidylserine unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_IgMAntiphosphatidylserineUnitPerMilliliter: Coding = {
  code: "{MPS'U}/mL",
  display: 'IgM antiphosphatidylserine unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MultOfMean: Coding = {
  code: '{MultOfMean}',
  display: 'MultOfMean',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Mutation: Coding = {
  code: '{mutation}',
  display: 'mutation',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NonspecificOunce: Coding = {
  code: '{NonspecificOunce}',
  display: 'NonspecificOunce',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Number_2: Coding = {
  code: '{Number}',
  display: 'Number',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_OpticalDensityUnit: Coding = {
  code: '{OD_unit}',
  display: 'optical density unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_OnceDay: Coding = {
  code: '{Once}/d',
  display: 'Once / day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_OpticalDensity: Coding = {
  code: '{OpticalDensity}',
  display: 'OpticalDensity',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_OpticalDensityIndex: Coding = {
  code: '{OpticalDensityIndex}',
  display: 'OpticalDensityIndex',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_OpticalDensityRatio: Coding = {
  code: '{OpticalDensityRatio}',
  display: 'OpticalDensityRatio',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_P2Y12ReactionUnits: Coding = {
  code: '{P2Y12 Reaction Units}',
  display: 'P2Y12 Reaction Units',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Package: Coding = {
  code: '{Package}',
  display: 'Package',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PacksDay: Coding = {
  code: '{Packs}/d',
  display: 'Packs / day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PackYears: Coding = {
  code: '{PackYears}',
  display: 'PackYears',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PanbioUnit: Coding = {
  code: "{Pan_Bio'U}",
  display: 'panbio unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Patch: Coding = {
  code: '{Patch}',
  display: 'Patch',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Percentile: Coding = {
  code: '{percentile}',
  display: 'percentile',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Percentile_2: Coding = {
  code: '{Percentile}',
  display: 'Percentile',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Phenotype: Coding = {
  code: '{phenotype}',
  display: 'phenotype',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Pill: Coding = {
  code: '{Pill}',
  display: 'Pill',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PouchesWeek: Coding = {
  code: '{Pouches}/wk',
  display: 'Pouches / week',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RadioactiveT3UptakeRatio: Coding = {
  code: '{RadioactiveT3UptakeRatio}',
  display: 'RadioactiveT3UptakeRatio',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Ratio: Coding = {
  code: '{ratio}',
  display: 'ratio',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RedBloodCellCount: Coding = {
  code: '{rbc}',
  display: 'red blood cell count',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RedBloodCellPerMicroliter: Coding = {
  code: '{RBC}/uL',
  display: 'red blood cell per microliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RecTiter: Coding = {
  code: '{RecTiter}',
  display: 'RecTiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RelativeSaturation: Coding = {
  code: '{relative_saturation}',
  display: 'relative saturation',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RelativeQuoteU: Coding = {
  code: "{Relative'U}",
  display: "Relative'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RelativeViscosity: Coding = {
  code: '{RelativeViscosity}',
  display: 'RelativeViscosity',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RPIQuoteU: Coding = {
  code: "{RPI'U}",
  display: "RPI'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RubellaVirus: Coding = {
  code: '{Rubella_virus}',
  display: 'rubella virus',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RubellaVirus_2: Coding = {
  code: '{RubellaVirus}',
  display: 'RubellaVirus',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SignalToCutoffRatio: Coding = {
  code: '{s_co_ratio}',
  display: 'signal to cutoff ratio',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SatIndex: Coding = {
  code: '{SatIndex}',
  display: 'SatIndex',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Saturation: Coding = {
  code: '{saturation}',
  display: 'saturation',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Scoop: Coding = {
  code: '{Scoop}',
  display: 'Scoop',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ScoreOf: Coding = {
  code: '{ScoreOf}',
  display: 'ScoreOf',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Shift: Coding = {
  code: '{shift}',
  display: 'shift',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SpermatozoaPerMilliliter: Coding = {
  code: '{spermatozoa}/mL',
  display: 'spermatozoa per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SpermatozoaPerMilliLiter: Coding = {
  code: '{Spermatozoa}/mL',
  display: 'SpermatozoaPerMilliLiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Spray: Coding = {
  code: '{spray}',
  display: 'spray',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_StandardDeviation: Coding = {
  code: '{StandardDeviation}',
  display: 'StandardDeviation',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_StandardIgAQuoteU: Coding = {
  code: "{StandardIgA'U}",
  display: "StandardIgA'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_StandardIgGQuoteU: Coding = {
  code: "{StandardIgG'U}",
  display: "StandardIgG'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_StandardIgMQuoteU: Coding = {
  code: "{StandardIgM'U}",
  display: "StandardIgM'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_StdDeviationQuoteU: Coding = {
  code: "{StdDeviation'U}",
  display: "StdDeviation'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_StandardDeviation_2: Coding = {
  code: '{STDV}',
  display: 'standard deviation',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_StimulatingIndex: Coding = {
  code: '{StimulatingIndex}',
  display: 'StimulatingIndex',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_StreptozymeQuoteU: Coding = {
  code: "{Streptozyme'U}",
  display: "Streptozyme'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Tablets: Coding = {
  code: '{tbl}',
  display: 'tablets',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ThyroxinUptakeQuoteU: Coding = {
  code: "{ThyroxinUptake'U}",
  display: "ThyroxinUptake'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TIBCQuoteU: Coding = {
  code: "{TIBC'U}",
  display: "TIBC'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TimesWeek: Coding = {
  code: '{Times}/wk',
  display: 'Times / week',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TineQuoteU: Coding = {
  code: "{Tine'U}",
  display: "Tine'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Titer: Coding = {
  code: '{titer}',
  display: 'titer',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ParticlesTotalCount: Coding = {
  code: '{tot}',
  display: 'particles total count',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ToxoplasmaIndexValue: Coding = {
  code: '{ToxoplasmaIndexValue}',
  display: 'ToxoplasmaIndexValue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ThyroidStimulatingImmunoglobulinIndex: Coding = {
  code: '{TSI_index}',
  display: 'thyroid-stimulating immunoglobulin index',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Vial: Coding = {
  code: '{Vial}',
  display: 'Vial',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VolumeVvolume: Coding = {
  code: '{Volume}/{Vvolume}',
  display: 'Volume / Vvolume',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_WhiteBloodCells: Coding = {
  code: '{WBCs}',
  display: 'white blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_WeeksDays: Coding = {
  code: '{WeeksDays}',
  display: 'WeeksDays',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_WhiteBloodCell: Coding = {
  code: '{WhiteBloodCell}',
  display: 'WhiteBloodCell',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VAL1Asterisk: Coding = {
  code: '1',
  display: '1*',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_OnePerDay: Coding = {
  code: '1/d',
  display: 'one per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_OnePerMinute: Coding = {
  code: '1/min',
  display: 'one per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TheNumberTenForArbitraryPowers: Coding = {
  code: '10*',
  display: 'the number ten for arbitrary powers',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TrillionPerLiter: Coding = {
  code: '10*12/L',
  display: 'trillion per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Thousand: Coding = {
  code: '10*3',
  display: 'Thousand',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ThousandRedBloodCells: Coding = {
  code: '10*3.{RBC}',
  display: 'Thousand Red Blood Cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ThousandPerAsteriskUnit: Coding = {
  code: '10*3.U',
  display: 'Thousand Per * Unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ThousandPerLiter: Coding = {
  code: '10*3/L',
  display: 'Thousand Per Liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ThousandPerMilliLiter: Coding = {
  code: '10*3/mL',
  display: 'Thousand Per MilliLiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ThousandsPerMicroLiter: Coding = {
  code: '10*3/uL',
  display: 'Thousands Per MicroLiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ThousandCopiesPerMilliliter: Coding = {
  code: '10*3{copies}/mL',
  display: 'thousand copies per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ThousandCopiesPerMilliLiter: Coding = {
  code: '10*3{Copies}/mL',
  display: 'Thousand Copies Per MilliLiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TheNumberTenForArbitraryPowersPower3PolarizationQuoteU: Coding = {
  code: "10*-3{Polarization'U}",
  display: "(the number ten for arbitrary powers ^ -3) Polarization'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ThousandRedBloodCells_2: Coding = {
  code: '10*3{RBCs}',
  display: 'thousand red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VAL10ThousandPerMicroliter: Coding = {
  code: '10*4/uL',
  display: '10 thousand per microliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_OneHundredThousand: Coding = {
  code: '10*5',
  display: 'OneHundredThousand',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Million: Coding = {
  code: '10*6',
  display: 'Million',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillionColonyFormingUnitPerLiter: Coding = {
  code: '10*6.[CFU]/L',
  display: 'million colony forming unit per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillionInternationalUnit: Coding = {
  code: '10*6.[iU]',
  display: 'MillionInternationalUnit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillionInternationalUnit_2: Coding = {
  code: '10*6.[IU]',
  display: 'million international unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillionEquivalentsPerMilliLiter: Coding = {
  code: '10*6.eq/mL',
  display: 'MillionEquivalentsPerMilliLiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TheNumberTenForArbitraryPowersPower6AsteriskUnit: Coding = {
  code: '10*6.U',
  display: '(the number ten for arbitrary powers ^ 6) * Unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillionPer24Hour: Coding = {
  code: '10*6/(24.h)',
  display: 'million per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillionPerSpecimen: Coding = {
  code: '10*6/{Specimen}',
  display: 'MillionPerSpecimen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillionPerKilogram: Coding = {
  code: '10*6/kg',
  display: 'million per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillionPerLiter: Coding = {
  code: '10*6/L',
  display: 'million per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillionPerMilliliter: Coding = {
  code: '10*6/mL',
  display: 'million per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TheNumberTenForArbitraryPowersPower6MillimeterPower3: Coding = {
  code: '10*6/mm3',
  display: '(the number ten for arbitrary powers ^ 6) / (millimeter ^ 3)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillionPerMicroliter: Coding = {
  code: '10*6/uL',
  display: 'million per microliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TheNumberTenForArbitraryPowersPower6ImmunofluorescenceQuoteU: Coding = {
  code: "10*-6{Immunofluorescence'U}",
  display: "(the number ten for arbitrary powers ^ -6) Immunofluorescence'U",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TenToEighth: Coding = {
  code: '10*8',
  display: 'TenToEighth',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BillionPerLiter: Coding = {
  code: '10*9/L',
  display: 'billion per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BillionPerMilliliter: Coding = {
  code: '10*9/mL',
  display: 'billion per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BillionPerMicroliter: Coding = {
  code: '10*9/uL',
  display: 'billion per microliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VAL10LiterPerMinutePerSquareMeter: Coding = {
  code: '10.L/(min.m2)',
  display: '10 liter per minute per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VAL10LiterPerMinute: Coding = {
  code: '10.L/min',
  display: '10 liter per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VAL10AsteriskMicroNewtonAsteriskSecondCentimeterAsteriskMeterPower2: Coding = {
  code: '10.uN.s/(cm.m2)',
  display: '10 * microNewton * second / centimeter * (meter ^ 2)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VAL10MicronewtonSecondPerCentimeterToTheFifthPowerPerSquareMeter: Coding = {
  code: '10.uN.s/(cm5.m2)',
  display: '10 micronewton second per centimeter to the fifth power per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VAL10AsteriskMicroNewtonAsteriskSecondCentimeter: Coding = {
  code: '10.uN.s/cm',
  display: '10 * microNewton * second / centimeter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VAL10AsteriskMicroNewtonAsteriskSecondCentimeterPower2: Coding = {
  code: '10.uN.s/cm2',
  display: '10 * microNewton * second / (centimeter ^ 2)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TheNumberTenForArbitraryPowers_2: Coding = {
  code: '10^',
  display: 'the number ten for arbitrary powers',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_VAL24Hour: Coding = {
  code: '24.h',
  display: '24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Ampre: Coding = {
  code: 'A',
  display: 'Ampère',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_AmpreMeter: Coding = {
  code: 'A/m',
  display: 'Ampère / meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MeanGregorianYear: Coding = {
  code: 'a_g',
  display: 'mean Gregorian year',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MeanJulianYear: Coding = {
  code: 'a_j',
  display: 'mean Julian year',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TropicalYear: Coding = {
  code: 'a_t',
  display: 'tropical year',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_AttogramPerCell: Coding = {
  code: 'ag/{cell}',
  display: 'attogram per cell',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Ngstrm: Coding = {
  code: 'Ao',
  display: 'Ångström',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Are: Coding = {
  code: 'ar',
  display: 'are',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_StandardAtmosphere: Coding = {
  code: 'atm',
  display: 'standard atmosphere',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_TechnicalAtmosphere: Coding = {
  code: 'att',
  display: 'technical atmosphere',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_AstronomicUnit: Coding = {
  code: 'AU',
  display: 'astronomic unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Barn: Coding = {
  code: 'b',
  display: 'barn',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Bel: Coding = {
  code: 'B',
  display: 'bel',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BelKilowatt: Coding = {
  code: 'B[kW]',
  display: 'bel kilowatt',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BelMillivolt: Coding = {
  code: 'B[mV]',
  display: 'bel millivolt',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BelSoundPressure: Coding = {
  code: 'B[SPL]',
  display: 'bel sound pressure',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BelMicrovolt: Coding = {
  code: 'B[uV]',
  display: 'bel microvolt',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BelVolt: Coding = {
  code: 'B[V]',
  display: 'bel volt',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_BelWatt: Coding = {
  code: 'B[W]',
  display: 'bel watt',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Bar: Coding = {
  code: 'bar',
  display: 'bar',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Baud: Coding = {
  code: 'Bd',
  display: 'baud',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Biot: Coding = {
  code: 'Bi',
  display: 'Biot',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Bit: Coding = {
  code: 'bit',
  display: 'bit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Bit_2: Coding = {
  code: 'bit_s',
  display: 'bit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Becquerel: Coding = {
  code: 'Bq',
  display: 'Becquerel',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Byte: Coding = {
  code: 'By',
  display: 'byte',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Coulomb: Coding = {
  code: 'C',
  display: 'Coulomb',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Calorie: Coding = {
  code: 'cal',
  display: 'calorie',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CalorieAt15DegreesC: Coding = {
  code: 'cal_[15]',
  display: 'calorie at 15 °C',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CalorieAt20DegreesC: Coding = {
  code: 'cal_[20]',
  display: 'calorie at 20 °C',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_InternationalTableCalorie: Coding = {
  code: 'cal_IT',
  display: 'international table calorie',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MeanCalorie: Coding = {
  code: 'cal_m',
  display: 'mean calorie',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_ThermochemicalCalorie: Coding = {
  code: 'cal_th',
  display: 'thermochemical calorie',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Candela: Coding = {
  code: 'cd',
  display: 'candela',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Centigram: Coding = {
  code: 'cg',
  display: 'centigram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CURIE: Coding = {
  code: 'Ci',
  display: 'CURIE',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Circle: Coding = {
  code: 'circ',
  display: 'circle',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Centiliter: Coding = {
  code: 'cL',
  display: 'centiliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CentimeterOfWater: Coding = {
  code: 'cm[H2O]',
  display: 'centimeter of water',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CentimeterOfWaterColumnSecondAsteriskMeter: Coding = {
  code: 'cm[H2O]/(s.m)',
  display: 'centimeter of water column / second * meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CentimeterOfWaterPerLiterPerSecond: Coding = {
  code: 'cm[H2O]/L/s',
  display: 'centimeter of water per liter per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CentimeterOfWaterPerSecondPerMeter: Coding = {
  code: 'cm[H2O]/s/m',
  display: 'centimeter of water per second per meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CentimeterOfMercury: Coding = {
  code: 'cm[Hg]',
  display: 'centimeter of mercury',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SquareCentimeter: Coding = {
  code: 'cm2',
  display: 'square centimeter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SquareCentimeterPerSecond: Coding = {
  code: 'cm2/s',
  display: 'square centimeter per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CubicCentimeter: Coding = {
  code: 'cm3',
  display: 'cubic centimeter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CentiPoise: Coding = {
  code: 'cP',
  display: 'centiPoise',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CentiStokes: Coding = {
  code: 'cSt',
  display: 'centiStokes',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DekaliterPerMinute: Coding = {
  code: 'daL/min',
  display: 'dekaliter per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DekaliterPerMinutePerSquareMeter: Coding = {
  code: 'daL/min/m2',
  display: 'dekaliter per minute per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Decibel: Coding = {
  code: 'dB',
  display: 'decibel',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Degree: Coding = {
  code: 'deg',
  display: 'degree',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DegreePerSecond: Coding = {
  code: 'deg/s',
  display: 'degree per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Decigram: Coding = {
  code: 'dg',
  display: 'decigram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Deciliter: Coding = {
  code: 'dL',
  display: 'deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Decimeter: Coding = {
  code: 'dm',
  display: 'decimeter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SquareDecimeterPerSquareSecond: Coding = {
  code: 'dm2/s2',
  display: 'square decimeter per square second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Dyne: Coding = {
  code: 'dyn',
  display: 'dyne',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DyneSecondPerCentimeterPerSquareMeter: Coding = {
  code: 'dyn.s/(cm.m2)',
  display: 'dyne second per centimeter per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_DyneSecondPerCentimeter: Coding = {
  code: 'dyn.s/cm',
  display: 'dyne second per centimeter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Equivalents: Coding = {
  code: 'eq',
  display: 'equivalents',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EquivalentsLiter: Coding = {
  code: 'eq/L',
  display: 'equivalents / liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EquivalentsMilliliter: Coding = {
  code: 'eq/mL',
  display: 'equivalents / milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EquivalentsMillimole: Coding = {
  code: 'eq/mmol',
  display: 'equivalents / millimole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EquivalentsMicromole: Coding = {
  code: 'eq/umol',
  display: 'equivalents / micromole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Erg: Coding = {
  code: 'erg',
  display: 'erg',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Electronvolt: Coding = {
  code: 'eV',
  display: 'electronvolt',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Farad: Coding = {
  code: 'F',
  display: 'Farad',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Femtogram: Coding = {
  code: 'fg',
  display: 'femtogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Femtoliter: Coding = {
  code: 'fL',
  display: 'femtoliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FemtoliterNanoliter: Coding = {
  code: 'fL/nL',
  display: 'femtoliter / nanoliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Femtometer: Coding = {
  code: 'fm',
  display: 'femtometer',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Femtomole: Coding = {
  code: 'fmol',
  display: 'femtomole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FemtomolePerGram: Coding = {
  code: 'fmol/g',
  display: 'femtomole per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FemtomolePerLiter: Coding = {
  code: 'fmol/L',
  display: 'femtomole per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FemtomoleMilligram: Coding = {
  code: 'fmol/mg',
  display: 'femtomole / milligram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FemtomolePerMilligramOfCytosolProtein: Coding = {
  code: 'fmol/mg{cytosol_protein}',
  display: 'femtomole per milligram of cytosol protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FemtomolePerMilligramOfProtein: Coding = {
  code: 'fmol/mg{protein}',
  display: 'femtomole per milligram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_FemtomoleMilliliter: Coding = {
  code: 'fmol/mL',
  display: 'femtomole / milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Gauss: Coding = {
  code: 'G',
  display: 'Gauss',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPercent: Coding = {
  code: 'g%',
  display: 'gram percent',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramAsteriskMeter: Coding = {
  code: 'g.m',
  display: 'gram * meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramAsteriskMeterHbAsteriskMeterPower2: Coding = {
  code: 'g.m/({hb}.m2)',
  display: 'gram * meter / hb * (meter ^ 2)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramMeterPerHeartBeat: Coding = {
  code: 'g.m/{beat}',
  display: 'gram meter per heart beat',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramMeterPerHeartbeat: Coding = {
  code: 'g.m/{H.B.}',
  display: 'gram meter per heartbeat',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramAsteriskMeterHb: Coding = {
  code: 'g.m/{hb}',
  display: 'gram * meter / hb',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPer100Gram: Coding = {
  code: 'g/(100.g)',
  display: 'gram per 100 gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPer12Hour: Coding = {
  code: 'g/(12.h)',
  display: 'gram per 12 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPer24Hour: Coding = {
  code: 'g/(24.h)',
  display: 'gram per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPer3Days: Coding = {
  code: 'g/(3.d)',
  display: 'gram per 3 days',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPer4Hour: Coding = {
  code: 'g/(4.h)',
  display: 'gram per 4 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPer48Hour: Coding = {
  code: 'g/(48.h)',
  display: 'gram per 48 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPer5Hour: Coding = {
  code: 'g/(5.h)',
  display: 'gram per 5 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPer6Hour: Coding = {
  code: 'g/(6.h)',
  display: 'gram per 6 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPer72Hour: Coding = {
  code: 'g/(72.h)',
  display: 'gram per 72 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Gram8AsteriskHour: Coding = {
  code: 'g/(8.h)',
  display: 'gram / 8 * hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPer8HourShift: Coding = {
  code: 'g/(8.h){shift}',
  display: 'gram per 8 hour shift',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Gram8AsteriskKilogramAsteriskHour: Coding = {
  code: 'g/(8.kg.h)',
  display: 'gram / 8 * kilogram * hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramKilogramAsteriskHour: Coding = {
  code: 'g/(kg.h)',
  display: 'gram / kilogram * hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramKilogramAsteriskMinute: Coding = {
  code: 'g/(kg.min)',
  display: 'gram / kilogram * minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerSpecimen: Coding = {
  code: 'g/{specimen}',
  display: 'gram per specimen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerTotalOutput: Coding = {
  code: 'g/{total_output}',
  display: 'gram per total output',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerTotalWeight: Coding = {
  code: 'g/{total_weight}',
  display: 'gram per total weight',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramTotalWeight: Coding = {
  code: 'g/{TotalWeight}',
  display: 'gram / TotalWeight',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerCubicCentimeter: Coding = {
  code: 'g/cm3',
  display: 'gram per cubic centimeter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerDay: Coding = {
  code: 'g/d',
  display: 'gram per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerDeciliter: Coding = {
  code: 'g/dL',
  display: 'gram per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerGram: Coding = {
  code: 'g/g',
  display: 'gram per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramGramCre: Coding = {
  code: 'g/g{Cre}',
  display: 'gram / gram Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramGramCreat: Coding = {
  code: 'g/g{creat}',
  display: 'gram / gram creat',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerGramOfGlobulin: Coding = {
  code: 'g/g{globulin}',
  display: 'gram per gram of globulin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerGramOfTissue: Coding = {
  code: 'g/g{tissue}',
  display: 'gram per gram of tissue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerHour: Coding = {
  code: 'g/h',
  display: 'gram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerHourPerSquareMeter: Coding = {
  code: 'g/h/m2',
  display: 'gram per hour per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerKilogram: Coding = {
  code: 'g/kg',
  display: 'gram per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerKilogramPer8Hour: Coding = {
  code: 'g/kg/(8.h)',
  display: 'gram per  kilogram per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerKilogramPer8HourShift: Coding = {
  code: 'g/kg/(8.h){shift}',
  display: 'gram per kilogram per 8 hour shift',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerKilogramPerDay: Coding = {
  code: 'g/kg/d',
  display: 'gram per kilogram per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerKilogramPerHour: Coding = {
  code: 'g/kg/h',
  display: 'gram per kilogram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerKilogramPerMinute: Coding = {
  code: 'g/kg/min',
  display: 'gram per kilogram per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerLiter: Coding = {
  code: 'g/L',
  display: 'gram per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramsPerSquareMeter: Coding = {
  code: 'g/m2',
  display: 'grams Per Square Meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerMilligram: Coding = {
  code: 'g/mg',
  display: 'gram per milligram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerMinute: Coding = {
  code: 'g/min',
  display: 'gram per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerMilliliter: Coding = {
  code: 'g/mL',
  display: 'gram per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerMillimole: Coding = {
  code: 'g/mmol',
  display: 'gram per millimole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramMillimoleCreat: Coding = {
  code: 'g/mmol{creat}',
  display: 'gram / millimole creat',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerMole: Coding = {
  code: 'g/mol',
  display: 'gram per mole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramPerMoleOfCreatinine: Coding = {
  code: 'g/mol{creat}',
  display: 'gram per mole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramOfCreatinine: Coding = {
  code: 'g{creat}',
  display: 'gram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramOfHemoglobin: Coding = {
  code: 'g{Hb}',
  display: 'gram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramOfTotalNitrogen: Coding = {
  code: 'g{total_nit}',
  display: 'gram of total nitrogen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramOfTotalProtein: Coding = {
  code: 'g{total_prot}',
  display: 'gram of total protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramOfWetTissue: Coding = {
  code: 'g{wet_tissue}',
  display: 'gram of wet tissue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Gal: Coding = {
  code: 'Gal',
  display: 'Gal',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Gilbert: Coding = {
  code: 'Gb',
  display: 'Gilbert',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GigaBecquerel: Coding = {
  code: 'GBq',
  display: 'gigaBecquerel',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GramForce: Coding = {
  code: 'gf',
  display: 'gram-force',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GonGrade: Coding = {
  code: 'gon',
  display: 'gon grade',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Gray: Coding = {
  code: 'Gy',
  display: 'Gray',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Henry: Coding = {
  code: 'H',
  display: 'Henry',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Hectoliter: Coding = {
  code: 'hL',
  display: 'hectoliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Hertz: Coding = {
  code: 'Hz',
  display: 'Hertz',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Joule: Coding = {
  code: 'J',
  display: 'joule',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_JoulePerLiter: Coding = {
  code: 'J/L',
  display: 'joule per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Kelvin: Coding = {
  code: 'K',
  display: 'Kelvin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KelvinWatt: Coding = {
  code: 'K/W',
  display: 'Kelvin / Watt',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KiloInternationalUnitPerLiter: Coding = {
  code: 'k[IU]/L',
  display: 'kilo international unit per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KiloInternationalUnitsPerMilliLiter: Coding = {
  code: 'k[iU]/mL',
  display: 'KiloInternationalUnitsPerMilliLiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KiloInternationalUnitPerMilliliter: Coding = {
  code: 'k[IU]/mL',
  display: 'kilo international unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Katal: Coding = {
  code: 'kat',
  display: 'katal',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KatalKilogram: Coding = {
  code: 'kat/kg',
  display: 'katal / kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KatalLiter: Coding = {
  code: 'kat/L',
  display: 'katal / liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KiloBecquerel: Coding = {
  code: 'kBq',
  display: 'kiloBecquerel',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Kilocalorie: Coding = {
  code: 'kcal',
  display: 'kilocalorie',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Kilocalorie8AsteriskHour: Coding = {
  code: 'kcal/(8.h)',
  display: 'kilocalorie / 8 * hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilocaloriePerOunceUSAndBritish: Coding = {
  code: 'kcal/[oz_av]',
  display: 'kilocalorie per ounce (US & British)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilocaloriePerDay: Coding = {
  code: 'kcal/d',
  display: 'kilocalorie per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilocaloriePerHour: Coding = {
  code: 'kcal/h',
  display: 'kilocalorie per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilocaloriePerKilogramPer24Hour: Coding = {
  code: 'kcal/kg/(24.h)',
  display: 'kilocalorie per kilogram per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilogramMeterPerSecond: Coding = {
  code: 'kg.m/s',
  display: 'kilogram meter per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilogramPerSecondPerSquareMeter: Coding = {
  code: 'kg/(s.m2)',
  display: 'kilogram per second per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilogramPerHour: Coding = {
  code: 'kg/h',
  display: 'kilogram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilogramPerLiter: Coding = {
  code: 'kg/L',
  display: 'kilogram per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilogramMeterPower2: Coding = {
  code: 'kg/m2',
  display: 'kilogram / (meter ^ 2)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilogramMeterPower3: Coding = {
  code: 'kg/m3',
  display: 'kilogram / (meter ^ 3)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilogramMinute: Coding = {
  code: 'kg/min',
  display: 'kilogram / minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilogramPerMole: Coding = {
  code: 'kg/mol',
  display: 'kilogram per mole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilogramSecond: Coding = {
  code: 'kg/s',
  display: 'kilogram / second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KilogramOfWetTissue: Coding = {
  code: "kg{wet'tis}",
  display: 'kilogram of wet tissue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Kiloliter: Coding = {
  code: 'kL',
  display: 'kiloliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KiloPascal: Coding = {
  code: 'kPa',
  display: 'kiloPascal',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Kilosecond: Coding = {
  code: 'ks',
  display: 'kilosecond',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KiloEnzymeUnit: Coding = {
  code: 'kU',
  display: 'kilo enzyme unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KiloenzymeUnitPerGram: Coding = {
  code: 'kU/g',
  display: 'kiloenzyme Unit per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KiloUnitHour: Coding = {
  code: 'kU/h',
  display: 'kiloUnit / hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KiloenzymeUnitPerLiter: Coding = {
  code: 'kU/L',
  display: 'kiloenzyme Unit per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KiloEnzymeUnitPerLiterClass: Coding = {
  code: 'kU/L{class}',
  display: 'kilo enzyme unit per liter class',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_KiloEnzymeUnitPerMilliliter: Coding = {
  code: 'kU/mL',
  display: 'kilo enzyme unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Kayser: Coding = {
  code: 'Ky',
  display: 'Kayser',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Liter: Coding = {
  code: 'L',
  display: 'liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LiterAsteriskSecondPower2Second: Coding = {
  code: 'L.s2/s',
  display: 'liter * (second ^ 2) / second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LiterPer24Hour: Coding = {
  code: 'L/(24.h)',
  display: 'liter per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LiterPer8Hour: Coding = {
  code: 'L/(8.h)',
  display: 'liter per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LiterPerMinutePerSquareMeter: Coding = {
  code: 'L/(min.m2)',
  display: 'liter per minute per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LiterPerDay: Coding = {
  code: 'L/d',
  display: 'liter per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LiterPerHour: Coding = {
  code: 'L/h',
  display: 'liter per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LiterPerKilogram: Coding = {
  code: 'L/kg',
  display: 'liter per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LiterPerLiter: Coding = {
  code: 'L/L',
  display: 'liter per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LiterPerMinute: Coding = {
  code: 'L/min',
  display: 'liter per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LiterSecond: Coding = {
  code: 'L/s',
  display: 'liter / second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LiterPerSecondPerSquareSecond: Coding = {
  code: 'L/s/s2',
  display: 'liter per second per square second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Lumen: Coding = {
  code: 'lm',
  display: 'lumen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LumenSquareMeter: Coding = {
  code: 'lm.m2',
  display: 'lumen square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_LumenMeterPower2: Coding = {
  code: 'lm/m2',
  display: 'lumen / (meter ^ 2)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Lambert: Coding = {
  code: 'Lmb',
  display: 'Lambert',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Lux: Coding = {
  code: 'lx',
  display: 'lux',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MeterPerSecond: Coding = {
  code: 'm/s',
  display: 'meter per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MeterPerSquareSecond: Coding = {
  code: 'm/s2',
  display: 'meter per square second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MeterOfWaterColumn: Coding = {
  code: 'm[H2O]',
  display: 'meter of water column',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MeterOfMercuryColumn: Coding = {
  code: 'm[Hg]',
  display: 'meter of mercury column',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliinternationalUnit: Coding = {
  code: 'm[iU]',
  display: 'milliinternational unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliInternationalUnitPerLiter: Coding = {
  code: 'm[IU]/L',
  display: 'milli international unit per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliInternationalUnitPerMilliliter: Coding = {
  code: 'm[IU]/mL',
  display: 'milli international unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SquareMeter: Coding = {
  code: 'm2',
  display: 'square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SquareMeterPerSecond: Coding = {
  code: 'm2/s',
  display: 'square meter per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CubicMeterPerSecond: Coding = {
  code: 'm3/s',
  display: 'cubic meter per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliAmpre: Coding = {
  code: 'mA',
  display: 'milliAmpère',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Millibar: Coding = {
  code: 'mbar',
  display: 'millibar',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillibarSecondPerLiter: Coding = {
  code: 'mbar.s/L',
  display: 'millibar second per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillibarPerLiterPerSecond: Coding = {
  code: 'mbar/L/s',
  display: 'millibar per liter per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MegaBecquerel: Coding = {
  code: 'MBq',
  display: 'megaBecquerel',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliCurie: Coding = {
  code: 'mCi',
  display: 'milliCurie',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Milliequivalent: Coding = {
  code: 'meq',
  display: 'milliequivalent',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPer12Hour: Coding = {
  code: 'meq/(12.h)',
  display: 'milliequivalent per 12 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPer2Hour: Coding = {
  code: 'meq/(2.h)',
  display: 'milliequivalent per 2 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPer24Hour: Coding = {
  code: 'meq/(24.h)',
  display: 'milliequivalent per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPer8Hour: Coding = {
  code: 'meq/(8.h)',
  display: 'milliequivalent per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Milliequivalents8AsteriskHourAsteriskKilogram: Coding = {
  code: 'meq/(8.h.kg)',
  display: 'milliequivalents / 8 * hour * kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentsKilogramAsteriskDay: Coding = {
  code: 'meq/(kg.d)',
  display: 'milliequivalents / kilogram * day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPerSpecimen: Coding = {
  code: 'meq/{specimen}',
  display: 'milliequivalent per specimen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentsSpecimen: Coding = {
  code: 'meq/{Specimen}',
  display: 'milliequivalents / Specimen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPerTotalVolume: Coding = {
  code: 'meq/{total_volume}',
  display: 'milliequivalent per total volume',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPerDay: Coding = {
  code: 'meq/d',
  display: 'milliequivalent per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPerDeciliter: Coding = {
  code: 'meq/dL',
  display: 'milliequivalent per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPerGram: Coding = {
  code: 'meq/g',
  display: 'milliequivalent per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentsGramCre: Coding = {
  code: 'meq/g{Cre}',
  display: 'milliequivalents / gram Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPerGramOfCreatinine: Coding = {
  code: 'meq/g{creat}',
  display: 'milliequivalent per gram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPerHour: Coding = {
  code: 'meq/h',
  display: 'milliequivalent per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPerKilogram: Coding = {
  code: 'meq/kg',
  display: 'milliequivalent per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPerKilogramPerHour: Coding = {
  code: 'meq/kg/h',
  display: 'milliequivalent per kilogram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentsKilogramMinute: Coding = {
  code: 'meq/kg/min',
  display: 'milliequivalents / kilogram / minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPerLiter: Coding = {
  code: 'meq/L',
  display: 'milliequivalent per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPerSquareMeter: Coding = {
  code: 'meq/m2',
  display: 'milliequivalent per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPerMinute: Coding = {
  code: 'meq/min',
  display: 'milliequivalent per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliequivalentPerMilliliter: Coding = {
  code: 'meq/mL',
  display: 'milliequivalent per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Milligram: Coding = {
  code: 'mg',
  display: 'milligram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPer10Hour: Coding = {
  code: 'mg/(10.h)',
  display: 'milligram per 10 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPer12Hour: Coding = {
  code: 'mg/(12.h)',
  display: 'milligram per 12 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPer18Hour: Coding = {
  code: 'mg/(18.h)',
  display: 'milligram per 18 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPer2Hour: Coding = {
  code: 'mg/(2.h)',
  display: 'milligram per 2 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPer24Hour: Coding = {
  code: 'mg/(24.h)',
  display: 'milligram per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPer6Hour: Coding = {
  code: 'mg/(6.h)',
  display: 'milligram per 6 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPer72Hour: Coding = {
  code: 'mg/(72.h)',
  display: 'milligram per 72 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPer8Hour: Coding = {
  code: 'mg/(8.h)',
  display: 'milligram per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Milligram8AsteriskHourAsteriskKilogram: Coding = {
  code: 'mg/(8.h.kg)',
  display: 'milligram / 8 * hour * kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramKilogramAsteriskHour: Coding = {
  code: 'mg/(kg.h)',
  display: 'milligram / kilogram * hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerCollection: Coding = {
  code: 'mg/{collection}',
  display: 'milligram per collection',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramHgbGram: Coding = {
  code: 'mg/{Hgb}/g',
  display: 'milligram / Hgb / gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerSpecimen: Coding = {
  code: 'mg/{specimen}',
  display: 'milligram per specimen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramSpecimen: Coding = {
  code: 'mg/{Specimen}',
  display: 'milligram / Specimen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerTotalOutput: Coding = {
  code: 'mg/{total_output}',
  display: 'milligram per total output',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerTotalVolume: Coding = {
  code: 'mg/{total_volume}',
  display: 'milligram per total volume',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliGramPerTotalVolume: Coding = {
  code: 'mg/{TotalVolume}',
  display: 'MilliGramPerTotalVolume',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramTotQuoteVolume: Coding = {
  code: "mg/{Tot'Volume}",
  display: "milligram / Tot'Volume",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramVolume: Coding = {
  code: 'mg/{Volume}',
  display: 'milligram / Volume',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerDay: Coding = {
  code: 'mg/d',
  display: 'milligram per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramDay173AsteriskTheNumberTenForArbitraryPowersPower2AsteriskMeterPower2: Coding = {
  code: 'mg/d/(173.10*-2.m2)',
  display: 'milligram / day / 173 * (the number ten for arbitrary powers ^ -2) * (meter ^ 2)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerDayPer173SquareMeter: Coding = {
  code: 'mg/d/{1.73_m2}',
  display: 'milligram per day per 1.73 square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerDeciliter: Coding = {
  code: 'mg/dL',
  display: 'milligram per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerDeciliterOfRedBloodCells: Coding = {
  code: 'mg/dL{RBCs}',
  display: 'milligram per deciliter of red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerGram: Coding = {
  code: 'mg/g',
  display: 'milligram per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramGramCre: Coding = {
  code: 'mg/g{Cre}',
  display: 'milligram / gram Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerGramOfCreatinine: Coding = {
  code: 'mg/g{creat}',
  display: 'milligram per gram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerGramOfDryTissue: Coding = {
  code: 'mg/g{dry_tissue}',
  display: 'milligram per gram of dry tissue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerGramOfFeces: Coding = {
  code: 'mg/g{feces}',
  display: 'milligram per gram of feces',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerGramOfTissue: Coding = {
  code: 'mg/g{tissue}',
  display: 'milligram per gram of tissue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerGramOfWetTissue: Coding = {
  code: 'mg/g{wet_tissue}',
  display: 'milligram per gram of wet tissue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerHour: Coding = {
  code: 'mg/h',
  display: 'milligram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerKilogram: Coding = {
  code: 'mg/kg',
  display: 'milligram per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramKilogram24AsteriskHour: Coding = {
  code: 'mg/kg/(24.h)',
  display: 'milligram / kilogram / 24 * hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerKilogramPer8Hour: Coding = {
  code: 'mg/kg/(8.h)',
  display: 'milligram per kilogram per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerKilogramPerDay: Coding = {
  code: 'mg/kg/d',
  display: 'milligram per kilogram per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerKilogramPerHour: Coding = {
  code: 'mg/kg/h',
  display: 'milligram per kilogram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerKilogramPerMinute: Coding = {
  code: 'mg/kg/min',
  display: 'milligram per kilogram per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerLiter: Coding = {
  code: 'mg/L',
  display: 'milligram per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerLiterOfRedBloodCells: Coding = {
  code: 'mg/L{RBCs}',
  display: 'milligram per liter of red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerSquareMeter: Coding = {
  code: 'mg/m2',
  display: 'milligram per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerCubicMeter: Coding = {
  code: 'mg/m3',
  display: 'milligram per cubic meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerMilligram: Coding = {
  code: 'mg/mg',
  display: 'milligram per milligram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramMilligramCre: Coding = {
  code: 'mg/mg{cre}',
  display: 'milligram / milligram cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramsPerMilligramCreatinine: Coding = {
  code: 'mg/mg{Cre}',
  display: 'MilligramsPerMilligramCreatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerMilligramOfCreatinine: Coding = {
  code: 'mg/mg{creat}',
  display: 'milligram per milligram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerMinute: Coding = {
  code: 'mg/min',
  display: 'milligram per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerMilliliter: Coding = {
  code: 'mg/mL',
  display: 'milligram per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerMillimole: Coding = {
  code: 'mg/mmol',
  display: 'milligram per millimole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramMillimoleCre: Coding = {
  code: 'mg/mmol{Cre}',
  display: 'milligram / millimole Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerMillimoleOfCreatinine: Coding = {
  code: 'mg/mmol{creat}',
  display: 'milligram per millimole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPerWeek: Coding = {
  code: 'mg/wk',
  display: 'milligram per week',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramOfCreatinine: Coding = {
  code: 'mg{creat}',
  display: 'milligram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramFibrinogenEquivalentUnitPerLiter: Coding = {
  code: 'mg{FEU}/L',
  display: 'milligram fibrinogen equivalent unit per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilligramPhenylketonesDeciliter: Coding = {
  code: 'mg{Phenylketones}/dL',
  display: 'milligram Phenylketones / deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Mho: Coding = {
  code: 'mho',
  display: 'mho',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Milliliter: Coding = {
  code: 'mL',
  display: 'milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterHQuoteBAsteriskMeterPower2: Coding = {
  code: "mL/({h'b}.m2)",
  display: "milliliter / h'b * (meter ^ 2)",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPer10Hour: Coding = {
  code: 'mL/(10.h)',
  display: 'milliliter per 10 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPer12Hour: Coding = {
  code: 'mL/(12.h)',
  display: 'milliliter per 12 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPer2Hour: Coding = {
  code: 'mL/(2.h)',
  display: 'milliliter per 2 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPer24Hour: Coding = {
  code: 'mL/(24.h)',
  display: 'milliliter per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPer4Hour: Coding = {
  code: 'mL/(4.h)',
  display: 'milliliter per 4 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPer5Hour: Coding = {
  code: 'mL/(5.h)',
  display: 'milliliter per 5 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPer6Hour: Coding = {
  code: 'mL/(6.h)',
  display: 'milliliter per 6 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPer72Hour: Coding = {
  code: 'mL/(72.h)',
  display: 'milliliter per 72 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPer8Hour: Coding = {
  code: 'mL/(8.h)',
  display: 'milliliter per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterKilogramAsteriskMinute: Coding = {
  code: 'mL/(kg.min)',
  display: 'milliliter / kilogram * minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerSquareInchInternational: Coding = {
  code: 'mL/[sin_i]',
  display: 'milliliter per square inch (international)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerHeartBeat: Coding = {
  code: 'mL/{beat}',
  display: 'milliliter per heart beat',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerHeartBeatPerSquareMeter: Coding = {
  code: 'mL/{beat}/m2',
  display: 'milliliter per heart beat per  square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliLitersPerHeartbeatSIVolumeUnits: Coding = {
  code: "mL/{h'b}",
  display: 'MilliLitersPerHeartbeat [SI Volume Units]',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterCentimeterOfWaterColumn: Coding = {
  code: 'mL/cm[H2O]',
  display: 'milliliter / centimeter of water column',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerDay: Coding = {
  code: 'mL/d',
  display: 'milliliter per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerDeciliter: Coding = {
  code: 'mL/dL',
  display: 'milliliter per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerHour: Coding = {
  code: 'mL/h',
  display: 'milliliter per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerKilogram: Coding = {
  code: 'mL/kg',
  display: 'milliliter per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerKilogramPer8Hour: Coding = {
  code: 'mL/kg/(8.h)',
  display: 'milliliter per kilogram per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerKilogramPerDay: Coding = {
  code: 'mL/kg/d',
  display: 'milliliter per kilogram per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerKilogramPerHour: Coding = {
  code: 'mL/kg/h',
  display: 'milliliter per kilogram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerKilogramPerMinute: Coding = {
  code: 'mL/kg/min',
  display: 'milliliter per kilogram per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerLiter: Coding = {
  code: 'mL/L',
  display: 'milliliter per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerSquareMeter: Coding = {
  code: 'mL/m2',
  display: 'milliliter per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerMillibar: Coding = {
  code: 'mL/mbar',
  display: 'milliliter per millibar',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerMinute: Coding = {
  code: 'mL/min',
  display: 'milliliter per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterMinute173AsteriskTheNumberTenForArbitraryPowersPower2AsteriskMeterPower2: Coding = {
  code: 'mL/min/(173.10*-2.m2)',
  display: 'milliliter / minute / 173 * (the number ten for arbitrary powers ^ -2) * (meter ^ 2)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerMinutePer173SquareMeter: Coding = {
  code: 'mL/min/{1.73_m2}',
  display: 'milliliter per minute per 1.73 square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerMinutePerSquareMeter: Coding = {
  code: 'mL/min/m2',
  display: 'milliliter per minute per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerMillimeter: Coding = {
  code: 'mL/mm',
  display: 'milliliter per millimeter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterPerSecond: Coding = {
  code: 'mL/s',
  display: 'milliliter per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliliterOfFetalRedBloodCells: Coding = {
  code: 'mL{fetal_RBCs}',
  display: 'milliliter of fetal red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimeterPerHour: Coding = {
  code: 'mm/h',
  display: 'millimeter per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimeterPerMinute: Coding = {
  code: 'mm/min',
  display: 'millimeter per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimeterOfWater: Coding = {
  code: 'mm[H2O]',
  display: 'millimeter of water',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimeterOfMercury: Coding = {
  code: 'mm[Hg]',
  display: 'millimeter of mercury',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SquareMillimeter: Coding = {
  code: 'mm2',
  display: 'square millimeter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_CubicMillimeter: Coding = {
  code: 'mm3',
  display: 'cubic millimeter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Millimole: Coding = {
  code: 'mmol',
  display: 'millimole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePer12Hour: Coding = {
  code: 'mmol/(12.h)',
  display: 'millimole per 12 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePer18Hour: Coding = {
  code: 'mmol/(18.h)',
  display: 'millimole per 18 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePer2Hour: Coding = {
  code: 'mmol/(2.h)',
  display: 'millimole per 2 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePer24Hour: Coding = {
  code: 'mmol/(24.h)',
  display: 'millimole per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePer5Hour: Coding = {
  code: 'mmol/(5.h)',
  display: 'millimole per 5 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePer6Hour: Coding = {
  code: 'mmol/(6.h)',
  display: 'millimole per 6 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePer8Hour: Coding = {
  code: 'mmol/(8.h)',
  display: 'millimole per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Millimole8AsteriskHourAsteriskKilogram: Coding = {
  code: 'mmol/(8.h.kg)',
  display: 'millimole / 8 * hour * kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerEjaculate: Coding = {
  code: 'mmol/{ejaculate}',
  display: 'millimole per ejaculate',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerSpecimen: Coding = {
  code: 'mmol/{specimen}',
  display: 'millimole per specimen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerTotalVolume: Coding = {
  code: 'mmol/{total_vol}',
  display: 'millimole per total volume',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliMolesPerTotalVolumeSubstanceUnits: Coding = {
  code: 'mmol/{TotalVolume}',
  display: 'MilliMolesPerTotalVolume [Substance Units]',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimoleTotQuoteVolume: Coding = {
  code: "mmol/{Tot'Volume}",
  display: "millimole / Tot'Volume",
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerDay: Coding = {
  code: 'mmol/d',
  display: 'millimole per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerDeciliter: Coding = {
  code: 'mmol/dL',
  display: 'millimole per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerGram: Coding = {
  code: 'mmol/g',
  display: 'millimole per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerGramOfCreatinine: Coding = {
  code: 'mmol/g{creat}',
  display: 'millimole per gram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerGramOfHemoglobin: Coding = {
  code: 'mmol/g{hemoglobin}',
  display: 'millimole per gram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerHour: Coding = {
  code: 'mmol/h',
  display: 'millimole per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerHourPerMilligramOfHemoglobin: Coding = {
  code: 'mmol/h/mg{Hb}',
  display: 'millimole per hour per milligram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerHourPerMilligramOfProtein: Coding = {
  code: 'mmol/h/mg{protein}',
  display: 'millimole per hour per milligram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerKilogram: Coding = {
  code: 'mmol/kg',
  display: 'millimole per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerKilogramPer8Hour: Coding = {
  code: 'mmol/kg/(8.h)',
  display: 'millimole per kilogram per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerKilogramPerDay: Coding = {
  code: 'mmol/kg/d',
  display: 'millimole per kilogram per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerKilogramPerHour: Coding = {
  code: 'mmol/kg/h',
  display: 'millimole per kilogram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerKilogramPerMinute: Coding = {
  code: 'mmol/kg/min',
  display: 'millimole per kilogram per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerKilogramOfWater: Coding = {
  code: 'mmol/kg{H2O}',
  display: 'millimole per kilogram of water',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerLiter: Coding = {
  code: 'mmol/L',
  display: 'millimole per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerLiterPerSecond: Coding = {
  code: 'mmol/L/s',
  display: 'millimole per liter per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerLiterOfRedBloodCells: Coding = {
  code: 'mmol/L{RBCs}',
  display: 'millimole per liter of red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimoleMeter: Coding = {
  code: 'mmol/m',
  display: 'millimole / meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerSquareMeter: Coding = {
  code: 'mmol/m2',
  display: 'millimole per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerMinute: Coding = {
  code: 'mmol/min',
  display: 'millimole per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerMillimole: Coding = {
  code: 'mmol/mmol',
  display: 'millimole per millimole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerMillmoleOfCreatinine: Coding = {
  code: 'mmol/mmol{creat}',
  display: 'millimole per millmole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerMillimoleOfUrea: Coding = {
  code: 'mmol/mmol{urea}',
  display: 'millimole per millimole of urea',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerMole: Coding = {
  code: 'mmol/mol',
  display: 'millimole per mole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliMolesPerMoleCreatinine: Coding = {
  code: 'mmol/mol{Cre}',
  display: 'MilliMolesPerMoleCreatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerMoleOfCreatinine: Coding = {
  code: 'mmol/mol{creat}',
  display: 'millimole per mole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillimolePerSecondPerLiter: Coding = {
  code: 'mmol/s/L',
  display: 'millimole per second per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MeanGregorianMonth: Coding = {
  code: 'mo_g',
  display: 'mean Gregorian month',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MeanJulianMonth: Coding = {
  code: 'mo_j',
  display: 'mean Julian month',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SynodalMonth: Coding = {
  code: 'mo_s',
  display: 'synodal month',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Mole: Coding = {
  code: 'mol',
  display: 'mole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MolePerDay: Coding = {
  code: 'mol/d',
  display: 'mole per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MolePerKilogram: Coding = {
  code: 'mol/kg',
  display: 'mole per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MolePerKilogramPerSecond: Coding = {
  code: 'mol/kg/s',
  display: 'mole per kilogram per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MolePerLiter: Coding = {
  code: 'mol/L',
  display: 'mole per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MolePerCubicMeter: Coding = {
  code: 'mol/m3',
  display: 'mole per cubic meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MolePerMilliliter: Coding = {
  code: 'mol/mL',
  display: 'mole per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MolePerMole: Coding = {
  code: 'mol/mol',
  display: 'mole per mole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MoleMoleCreat: Coding = {
  code: 'mol/mol{creat}',
  display: 'mole / mole creat',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MolePerSecond: Coding = {
  code: 'mol/s',
  display: 'mole per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Milliosmole: Coding = {
  code: 'mosm',
  display: 'milliosmole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliosmolePerKilogram: Coding = {
  code: 'mosm/kg',
  display: 'milliosmole per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliosmolePerLiter: Coding = {
  code: 'mosm/L',
  display: 'milliosmole per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Millipascal: Coding = {
  code: 'mPa',
  display: 'millipascal',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillipascalSecond: Coding = {
  code: 'mPa.s',
  display: 'millipascal second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Megasecond: Coding = {
  code: 'Ms',
  display: 'megasecond',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillienzymeUnit: Coding = {
  code: 'mU',
  display: 'millienzyme Unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillienzymeUnitPerGram: Coding = {
  code: 'mU/g',
  display: 'millienzyme Unit per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliEnzymeUnitPerGramOfHemoglobin: Coding = {
  code: 'mU/g{Hb}',
  display: 'milli enzyme unit per gram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliUnitGramHgb: Coding = {
  code: 'mU/g{Hgb}',
  display: 'milliUnit / gram Hgb',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliEnzymeUnitPerGramOfProtein: Coding = {
  code: 'mU/g{protein}',
  display: 'milli enzyme unit per gram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillienzymeUnitPerLiter: Coding = {
  code: 'mU/L',
  display: 'millienzyme Unit per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliUnitMilligram: Coding = {
  code: 'mU/mg',
  display: 'milliUnit / milligram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliUnitMilligramCre: Coding = {
  code: 'mU/mg{Cre}',
  display: 'milliUnit / milligram Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliEnzymeUnitPerMilligramOfCreatinine: Coding = {
  code: 'mU/mg{creat}',
  display: 'milli enzyme unit per milligram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliUnitMinute: Coding = {
  code: 'mU/min',
  display: 'milliUnit / minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillienzymeUnitPerMilliliter: Coding = {
  code: 'mU/mL',
  display: 'millienzyme Unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillienzymeUnitPerMilliliterPerMinute: Coding = {
  code: 'mU/mL/min',
  display: 'millienzyme Unit per milliliter per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliEnzymeUnitPerMillimoleOfCreatinine: Coding = {
  code: 'mU/mmol{creat}',
  display: 'milli  enzyme unit per millimole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillienzymeUnitPerMillimoleOfCreatinine: Coding = {
  code: 'mU/mmol{creatinine}',
  display: 'millienzyme Unit per millimole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MillienzymeUnitPerMillimoleOfRedBloodCells: Coding = {
  code: 'mU/mmol{RBCs}',
  display: 'millienzyme Unit per millimole of red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MilliVolt: Coding = {
  code: 'mV',
  display: 'milliVolt',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Maxwell: Coding = {
  code: 'Mx',
  display: 'Maxwell',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Newton: Coding = {
  code: 'N',
  display: 'Newton',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NewtonCentimeter: Coding = {
  code: 'N.cm',
  display: 'Newton centimeter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NewtonSecond: Coding = {
  code: 'N.s',
  display: 'Newton second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanoCurie: Coding = {
  code: 'nCi',
  display: 'nanoCurie',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Nanogram: Coding = {
  code: 'ng',
  display: 'nanogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPer24Hour: Coding = {
  code: 'ng/(24.h)',
  display: 'nanogram per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPer8Hour: Coding = {
  code: 'ng/(8.h)',
  display: 'nanogram per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Nanogram8AsteriskHourAsteriskKilogram: Coding = {
  code: 'ng/(8.h.kg)',
  display: 'nanogram / 8 * hour * kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramKilogramAsteriskDay: Coding = {
  code: 'ng/(kg.d)',
  display: 'nanogram / kilogram * day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramKilogramAsteriskHour: Coding = {
  code: 'ng/(kg.h)',
  display: 'nanogram / kilogram * hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramKilogramAsteriskMinute: Coding = {
  code: 'ng/(kg.min)',
  display: 'nanogram / kilogram * minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerMillion: Coding = {
  code: 'ng/10*6',
  display: 'nanogram per million',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerMillionRedBloodCells: Coding = {
  code: 'ng/10*6{RBCs}',
  display: 'nanogram per million red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerDay: Coding = {
  code: 'ng/d',
  display: 'nanogram per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerDeciliter: Coding = {
  code: 'ng/dL',
  display: 'nanogram per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramDeciliterHour: Coding = {
  code: 'ng/dL/h',
  display: 'nanogram / deciliter / hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerGram: Coding = {
  code: 'ng/g',
  display: 'nanogram per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramGramCre: Coding = {
  code: 'ng/g{Cre}',
  display: 'nanogram / gram Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerGramOfCreatinine: Coding = {
  code: 'ng/g{creat}',
  display: 'nanogram per gram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerHour: Coding = {
  code: 'ng/h',
  display: 'nanogram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerKilogram: Coding = {
  code: 'ng/kg',
  display: 'nanogram per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerKilogramPer8Hour: Coding = {
  code: 'ng/kg/(8.h)',
  display: 'nanogram per kilogram per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerKilogramPerHour: Coding = {
  code: 'ng/kg/h',
  display: 'nanogram per kilogram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerKilogramPerMinute: Coding = {
  code: 'ng/kg/min',
  display: 'nanogram per kilogram per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerLiter: Coding = {
  code: 'ng/L',
  display: 'nanogram per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerSquareMeter: Coding = {
  code: 'ng/m2',
  display: 'nanogram per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerMilligram: Coding = {
  code: 'ng/mg',
  display: 'nanogram per milligram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerMilligramPerHour: Coding = {
  code: 'ng/mg/h',
  display: 'nanogram per milligram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerMilligramOfCreatinine: Coding = {
  code: 'ng/mg{creat}',
  display: 'nanogram per milligram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerMilligramOfProtein: Coding = {
  code: 'ng/mg{protein}',
  display: 'nanogram per milligram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramMilligramProtein: Coding = {
  code: 'ng/mg{Protein}',
  display: 'nanogram / milligram Protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerMinute: Coding = {
  code: 'ng/min',
  display: 'nanogram per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerMillliiter: Coding = {
  code: 'ng/mL',
  display: 'nanogram per millliiter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerMilliliterPerHour: Coding = {
  code: 'ng/mL/h',
  display: 'nanogram per milliliter per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramMilliliterRbc: Coding = {
  code: 'ng/mL{rbc}',
  display: 'nanogram / milliliter rbc',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerMilliliterOfRedBloodCells: Coding = {
  code: 'ng/mL{RBCs}',
  display: 'nanogram per milliliter of red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerSecond: Coding = {
  code: 'ng/s',
  display: 'nanogram per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramPerEnzymeUnit: Coding = {
  code: 'ng/U',
  display: 'nanogram per enzyme unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanogramFibrinogenEquivalentUnitPerMilliliter: Coding = {
  code: 'ng{FEU}/mL',
  display: 'nanogram fibrinogen equivalent unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Nanokatal: Coding = {
  code: 'nkat',
  display: 'nanokatal',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Nanoliter: Coding = {
  code: 'nL',
  display: 'nanoliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanometerPerSecondPerLiter: Coding = {
  code: 'nm/s/L',
  display: 'nanometer per second per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Nanomole: Coding = {
  code: 'nmol',
  display: 'nanomole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePer24Hour: Coding = {
  code: 'nmol/(24.h)',
  display: 'nanomole per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerDay: Coding = {
  code: 'nmol/d',
  display: 'nanomole per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerDeciliter: Coding = {
  code: 'nmol/dL',
  display: 'nanomole per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerDeciliterOfGlomerularFiltrate: Coding = {
  code: 'nmol/dL{GF}',
  display: 'nanomole per deciliter of glomerular filtrate',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerGram: Coding = {
  code: 'nmol/g',
  display: 'nanomole per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomoleGramCre: Coding = {
  code: 'nmol/g{Cre}',
  display: 'nanomole / gram Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerGramOfCreatinine: Coding = {
  code: 'nmol/g{creat}',
  display: 'nanomole per gram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerGramOfDryWeight: Coding = {
  code: 'nmol/g{dry_wt}',
  display: 'nanomole per gram of dry weight',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerHourPerLiter: Coding = {
  code: 'nmol/h/L',
  display: 'nanomole per hour per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerHourPerMilligramOfProtein: Coding = {
  code: 'nmol/h/mg{protein}',
  display: 'nanomole per hour per milligram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerHourPerMilliliter: Coding = {
  code: 'nmol/h/mL',
  display: 'nanomole per hour per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerLiter: Coding = {
  code: 'nmol/L',
  display: 'nanomole per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerLiterPerMillimoleOfCreatinine: Coding = {
  code: 'nmol/L/mmol{creat}',
  display: 'nanomole per liter per millimole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerLiterPerSecond: Coding = {
  code: 'nmol/L/s',
  display: 'nanomole per liter per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerLiterOfRedBloodCells: Coding = {
  code: 'nmol/L{RBCs}',
  display: 'nanomole per liter of red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMeterPerMilligramOfProtein: Coding = {
  code: 'nmol/m/mg{protein}',
  display: 'nanomole per meter per milligram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMilligram: Coding = {
  code: 'nmol/mg',
  display: 'nanomole per milligram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMilligramPerHour: Coding = {
  code: 'nmol/mg/h',
  display: 'nanomole per milligram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMilligramOfCreatinine: Coding = {
  code: 'nmol/mg{creat}',
  display: 'nanomole per milligram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMilligramOfProtein: Coding = {
  code: 'nmol/mg{protein}',
  display: 'nanomole per milligram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMilligramOfProteinPerHour: Coding = {
  code: 'nmol/mg{protein}/h',
  display: 'nanomole per milligram of protein per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMinute: Coding = {
  code: 'nmol/min',
  display: 'nanomole per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMinutePerMillionCells: Coding = {
  code: 'nmol/min/10*6{cells}',
  display: 'nanomole per minute per million cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMinutePerMilligramOfHemoglobin: Coding = {
  code: 'nmol/min/mg{Hb}',
  display: 'nanomole per minute per milligram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMinutePerMilligramOfHemoglobin_2: Coding = {
  code: 'nmol/min/mg{hemoglobin}',
  display: 'nanomole per minute per milligram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMinutePerMilligramOfProtein: Coding = {
  code: 'nmol/min/mg{protein}',
  display: 'nanomole per minute per milligram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMinutePerMilliliter: Coding = {
  code: 'nmol/min/mL',
  display: 'nanomole per minute per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMilliliter: Coding = {
  code: 'nmol/mL',
  display: 'nanomole per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMilliliterPerHour: Coding = {
  code: 'nmol/mL/h',
  display: 'nanomole per milliliter per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMilliliterPerMinute: Coding = {
  code: 'nmol/mL/min',
  display: 'nanomole per milliliter per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMillimole: Coding = {
  code: 'nmol/mmol',
  display: 'nanomole per millimole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomoleMillimoleCre: Coding = {
  code: 'nmol/mmol{Cre}',
  display: 'nanomole / millimole Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMillimoleOfCreatinine: Coding = {
  code: 'nmol/mmol{creat}',
  display: 'nanomole per millimole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMole: Coding = {
  code: 'nmol/mol',
  display: 'nanomole per mole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerNanomole: Coding = {
  code: 'nmol/nmol',
  display: 'nanomole per nanomole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerSecond: Coding = {
  code: 'nmol/s',
  display: 'nanomole per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerSecondPerLiter: Coding = {
  code: 'nmol/s/L',
  display: 'nanomole per second per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomolePerMicromoleOfCreatinine: Coding = {
  code: 'nmol/umol{creat}',
  display: 'nanomole per micromole  of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomoleOf12CystinePerMilligramOfProtein: Coding = {
  code: 'nmol{1/2cys}/mg{protein}',
  display: 'nanomole of 1/2 cystine per milligram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomoleOfATP: Coding = {
  code: 'nmol{ATP}',
  display: 'nanomole of ATP',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomoleBoneCollagenEquivalent: Coding = {
  code: 'nmol{BCE}',
  display: 'nanomole bone collagen equivalent',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomoleBoneCollagenEquivalentPerLiter: Coding = {
  code: 'nmol{BCE}/L',
  display: 'nanomole bone collagen equivalent per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanomoleBoneCollagenEquivalentPerMillimoleOfCreatinine: Coding = {
  code: 'nmol{BCE}/mmol{creat}',
  display: 'nanomole bone collagen equivalent per millimole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Neper: Coding = {
  code: 'Np',
  display: 'neper',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Nanosecond: Coding = {
  code: 'ns',
  display: 'nanosecond',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanoenzymeUnitPerRedBloodCell: Coding = {
  code: 'nU/{RBC}',
  display: 'nanoenzyme unit per red blood cell',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_NanoenzymeUnitPerMilliliter: Coding = {
  code: 'nU/mL',
  display: 'nanoenzyme unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Oersted: Coding = {
  code: 'Oe',
  display: 'Oersted',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Ohm: Coding = {
  code: 'Ohm',
  display: 'Ohm',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_OhmMeter: Coding = {
  code: 'Ohm.m',
  display: 'Ohm meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Osmole: Coding = {
  code: 'osm',
  display: 'osmole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_OsmolePerKilogram: Coding = {
  code: 'osm/kg',
  display: 'osmole per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_OsmolePerLiter: Coding = {
  code: 'osm/L',
  display: 'osmole per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Poise: Coding = {
  code: 'P',
  display: 'Poise',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Picoampere: Coding = {
  code: 'pA',
  display: 'picoampere',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Pascal: Coding = {
  code: 'Pa',
  display: 'Pascal',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Parsec: Coding = {
  code: 'pc',
  display: 'parsec',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Picogram: Coding = {
  code: 'pg',
  display: 'picogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicogramPerCell: Coding = {
  code: 'pg/{cell}',
  display: 'picogram per cell',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicogramPerRedBloodCell: Coding = {
  code: 'pg/{RBC}',
  display: 'picogram per red blood cell',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicogramPerDeciliter: Coding = {
  code: 'pg/dL',
  display: 'picogram per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicogramPerLiter: Coding = {
  code: 'pg/L',
  display: 'picogram per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicogramPerMilligram: Coding = {
  code: 'pg/mg',
  display: 'picogram per milligram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicogramPerMilligramOfCreatinine: Coding = {
  code: 'pg/mg{creat}',
  display: 'picogram per milligram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicogramPerMilliliter: Coding = {
  code: 'pg/mL',
  display: 'picogram per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicogramPerMillimeter: Coding = {
  code: 'pg/mm',
  display: 'picogram per millimeter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Phot: Coding = {
  code: 'ph',
  display: 'phot',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Picokatal: Coding = {
  code: 'pkat',
  display: 'picokatal',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Picoliter: Coding = {
  code: 'pL',
  display: 'picoliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Picometer: Coding = {
  code: 'pm',
  display: 'picometer',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Picomole: Coding = {
  code: 'pmol',
  display: 'picomole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePer24Hour: Coding = {
  code: 'pmol/(24.h)',
  display: 'picomole per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerRedBloodCell: Coding = {
  code: 'pmol/{RBC}',
  display: 'picomole per red blood cell',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerDay: Coding = {
  code: 'pmol/d',
  display: 'picomole per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerDeciliter: Coding = {
  code: 'pmol/dL',
  display: 'picomole per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerGram: Coding = {
  code: 'pmol/g',
  display: 'picomole per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerHourPerMilligramOfProtein: Coding = {
  code: 'pmol/h/mg{protein}',
  display: 'picomole per hour per milligram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerHourPerMilliliter: Coding = {
  code: 'pmol/h/mL',
  display: 'picomole per hour per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerLiter: Coding = {
  code: 'pmol/L',
  display: 'picomole per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerMilligramOfProtein: Coding = {
  code: 'pmol/mg{protein}',
  display: 'picomole per milligram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerMinute: Coding = {
  code: 'pmol/min',
  display: 'picomole per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerMinutePerMilligramOfProtein: Coding = {
  code: 'pmol/min/mg{protein}',
  display: 'picomole per minute per milligram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerMilliliter: Coding = {
  code: 'pmol/mL',
  display: 'picomole per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerMillimole: Coding = {
  code: 'pmol/mmol',
  display: 'picomole per millimole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerMillimoleOfCreatinine: Coding = {
  code: 'pmol/mmol{creat}',
  display: 'picomole per millimole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerMole: Coding = {
  code: 'pmol/mol',
  display: 'picomole per mole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerMicromole: Coding = {
  code: 'pmol/umol',
  display: 'picomole per micromole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_PicomolePerMicromoleOfCreatinine: Coding = {
  code: 'pmol/umol{creat}',
  display: 'picomole per micromole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Picosecond: Coding = {
  code: 'ps',
  display: 'picosecond',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Picotesla: Coding = {
  code: 'pT',
  display: 'picotesla',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Roentgen: Coding = {
  code: 'R',
  display: 'Roentgen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Radian: Coding = {
  code: 'rad',
  display: 'radian',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RadiationAbsorbedDose: Coding = {
  code: 'RAD',
  display: 'radiation absorbed dose',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_RadiationEquivalentMan: Coding = {
  code: 'REM',
  display: 'radiation equivalent man',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Siemens: Coding = {
  code: 'S',
  display: 'Siemens',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_SecondPerControl: Coding = {
  code: 's/{control}',
  display: 'second per control',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Stilb: Coding = {
  code: 'sb',
  display: 'stilb',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Spere: Coding = {
  code: 'sph',
  display: 'spere',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Steradian: Coding = {
  code: 'sr',
  display: 'steradian',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Stere: Coding = {
  code: 'st',
  display: 'stere',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Stokes: Coding = {
  code: 'St',
  display: 'Stokes',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Sievert: Coding = {
  code: 'Sv',
  display: 'Sievert',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Tonne: Coding = {
  code: 't',
  display: 'tonne',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Tesla: Coding = {
  code: 'T',
  display: 'Tesla',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_UnifiedAtomicMassUnit: Coding = {
  code: 'u',
  display: 'unified atomic mass unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnit: Coding = {
  code: 'U',
  display: 'enzyme Unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPer1Hour: Coding = {
  code: 'U/(1.h)',
  display: 'enzyme Unit per 1 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPer10GramOfFeces: Coding = {
  code: 'U/(10.g){feces}',
  display: 'enzyme unit per 10 gram of feces',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPer12Hour: Coding = {
  code: 'U/(12.h)',
  display: 'enzyme unit per 12 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPer18Hour: Coding = {
  code: 'U/(18.h)',
  display: 'enzyme Unit per 18 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPer2Hour: Coding = {
  code: 'U/(2.h)',
  display: 'enzyme unit per 2 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPer24Hour: Coding = {
  code: 'U/(24.h)',
  display: 'enzyme unit per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPer10: Coding = {
  code: 'U/10',
  display: 'enzyme unit per 10',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPer10Billion: Coding = {
  code: 'U/10*10',
  display: 'enzyme unit per 10 billion',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPer10BillionCells: Coding = {
  code: 'U/10*10{cells}',
  display: 'enzyme unit per 10 billion cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerTrillion: Coding = {
  code: 'U/10*12',
  display: 'enzyme unit per trillion',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerTrillionRedBloodCells: Coding = {
  code: 'U/10*12{RBCs}',
  display: 'enzyme unit per trillion red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerMillion: Coding = {
  code: 'U/10*6',
  display: 'enzyme unit per million',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerBillion: Coding = {
  code: 'U/10*9',
  display: 'enzyme unit per billion',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerDay: Coding = {
  code: 'U/d',
  display: 'enzyme unit per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerDeciliter: Coding = {
  code: 'U/dL',
  display: 'enzyme unit per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerGram: Coding = {
  code: 'U/g',
  display: 'enzyme unit per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_UnitGramCre: Coding = {
  code: 'U/g{Cre}',
  display: 'Unit / gram Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerGramOfCreatinine: Coding = {
  code: 'U/g{creat}',
  display: 'enzyme unit per gram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerGramOfHemoglobin: Coding = {
  code: 'U/g{Hb}',
  display: 'enzyme unit per gram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerGramOfHemoglobin_2: Coding = {
  code: 'U/g{hemoglobin}',
  display: 'enzyme Unit per gram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_UnitsPerGramHemoglobin: Coding = {
  code: 'U/g{Hgb}',
  display: 'UnitsPerGramHemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerGramOfProtein: Coding = {
  code: 'U/g{protein}',
  display: 'enzyme unit per gram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerHour: Coding = {
  code: 'U/h',
  display: 'enzyme unit per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_UnitKilogramHour: Coding = {
  code: 'U/kg/h',
  display: 'Unit / kilogram / hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerKilogramOfHemoglobin: Coding = {
  code: 'U/kg{Hb}',
  display: 'enzyme unit per kilogram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerKilogramOfHemoglobin_2: Coding = {
  code: 'U/kg{hemoglobin}',
  display: 'enzyme Unit per kilogram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerLiter: Coding = {
  code: 'U/L',
  display: 'enzyme unit per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerMinute: Coding = {
  code: 'U/min',
  display: 'enzyme unit per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerMilliliter: Coding = {
  code: 'U/mL',
  display: 'enzyme unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_UnitMilliliterRBC: Coding = {
  code: 'U/mL{RBC}',
  display: 'Unit / milliliter RBC',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerMilliliterOfRedBloodCells: Coding = {
  code: 'U/mL{RBCs}',
  display: 'enzyme unit per milliliter of red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerMillimoleOfCreatinine: Coding = {
  code: 'U/mmol{creat}',
  display: 'enzyme unit per millimole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerMole: Coding = {
  code: 'U/mol',
  display: 'enzyme Unit per mole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerSecond: Coding = {
  code: 'U/s',
  display: 'enzyme unit per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerMicromole: Coding = {
  code: 'U/umol',
  display: 'enzyme Unit per micromole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicroInternationalUnit: Coding = {
  code: 'u[IU]',
  display: 'micro international unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrointernationalUnitPerLiter: Coding = {
  code: 'u[IU]/L',
  display: 'microinternational unit per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicroInternationalUnitPerMilliliter: Coding = {
  code: 'u[IU]/mL',
  display: 'micro international unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerLiterAt25DegCelsius: Coding = {
  code: 'U{25Cel}/L',
  display: 'enzyme unit per liter at 25 deg Celsius',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_EnzymeUnitPerLiterAt37DegCelsius: Coding = {
  code: 'U{37Cel}/L',
  display: 'enzyme unit per liter at 37 deg Celsius',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_GUnit: Coding = {
  code: 'U{G}',
  display: 'G unit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MICROCURIE: Coding = {
  code: 'uCi',
  display: 'MICROCURIE',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Microequivalents: Coding = {
  code: 'ueq',
  display: 'microequivalents',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicroequivalentPerLiter: Coding = {
  code: 'ueq/L',
  display: 'microequivalent per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicroequivalentPerMilliliter: Coding = {
  code: 'ueq/mL',
  display: 'microequivalent per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Microgram: Coding = {
  code: 'ug',
  display: 'microgram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPer100Gram: Coding = {
  code: 'ug/(100.g)',
  display: 'microgram per 100 gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPer24Hour: Coding = {
  code: 'ug/(24.h)',
  display: 'microgram per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPer8Hour: Coding = {
  code: 'ug/(8.h)',
  display: 'microgram per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramKilogramAsteriskDay: Coding = {
  code: 'ug/(kg.d)',
  display: 'microgram / kilogram * day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramKilogramAsteriskHour: Coding = {
  code: 'ug/(kg.h)',
  display: 'microgram / kilogram * hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerSquareFootInternational: Coding = {
  code: 'ug/[sft_i]',
  display: 'microgram per square foot (international)',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerSpecimen: Coding = {
  code: 'ug/{specimen}',
  display: 'microgram per specimen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramSpecimen: Coding = {
  code: 'ug/{Specimen}',
  display: 'microgram / Specimen',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicroGramsPerTotalVolume: Coding = {
  code: 'ug/{TotalVolume}',
  display: 'MicroGramsPerTotalVolume',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerDay: Coding = {
  code: 'ug/d',
  display: 'microgram per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerDeciliter: Coding = {
  code: 'ug/dL',
  display: 'microgram per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramDeciliterRbc: Coding = {
  code: 'ug/dL{rbc}',
  display: 'microgram / deciliter rbc',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerDeciliterOfRedBloodCells: Coding = {
  code: 'ug/dL{RBCs}',
  display: 'microgram per deciliter of red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerGram: Coding = {
  code: 'ug/g',
  display: 'microgram per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramGramCre: Coding = {
  code: 'ug/g{Cre}',
  display: 'microgram / gram Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerGramOfCreatinine: Coding = {
  code: 'ug/g{creat}',
  display: 'microgram per gram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerGramOfDryTissue: Coding = {
  code: 'ug/g{dry_tissue}',
  display: 'microgram per gram of dry tissue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerGramOfDryWeight: Coding = {
  code: 'ug/g{dry_wt}',
  display: 'microgram per gram of dry weight',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramGramDryWeight: Coding = {
  code: 'ug/g{DryWeight}',
  display: 'microgram / gram DryWeight',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerGramOfFeces: Coding = {
  code: 'ug/g{feces}',
  display: 'microgram  per gram of feces',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerGramOfHair: Coding = {
  code: 'ug/g{hair}',
  display: 'microgram per gram of hair',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerGramOfHemoglobin: Coding = {
  code: 'ug/g{Hb}',
  display: 'microgram per gram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramGramHgb: Coding = {
  code: 'ug/g{Hgb}',
  display: 'microgram / gram Hgb',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerGramOfTissue: Coding = {
  code: 'ug/g{tissue}',
  display: 'microgram per gram of tissue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramGramTissue: Coding = {
  code: 'ug/g{Tissue}',
  display: 'microgram / gram Tissue',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerHour: Coding = {
  code: 'ug/h',
  display: 'microgram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerKilogram: Coding = {
  code: 'ug/kg',
  display: 'microgram per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerKilogramPer8Hour: Coding = {
  code: 'ug/kg/(8.h)',
  display: 'microgram per kilogram per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerKilogramPerDay: Coding = {
  code: 'ug/kg/d',
  display: 'microgram per kilogram per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerKilogramPerHour: Coding = {
  code: 'ug/kg/h',
  display: 'microgram per kilogram per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerKilogramPerMinute: Coding = {
  code: 'ug/kg/min',
  display: 'microgram per kilogram per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerLiter: Coding = {
  code: 'ug/L',
  display: 'microgram per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerLiterPer24Hour: Coding = {
  code: 'ug/L/(24.h)',
  display: 'microgram per liter per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramLiterDDU: Coding = {
  code: 'ug/L{DDU}',
  display: 'microgram / liter DDU',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerLiterOfRedBloodCells: Coding = {
  code: 'ug/L{RBCs}',
  display: 'microgram per liter of red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerSquareMeter: Coding = {
  code: 'ug/m2',
  display: 'microgram per square meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerCubicMeter: Coding = {
  code: 'ug/m3',
  display: 'microgram per cubic meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerMilligram: Coding = {
  code: 'ug/mg',
  display: 'microgram per milligram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramMilligramCre: Coding = {
  code: 'ug/mg{Cre}',
  display: 'microgram / milligram Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerMilligramOfCreatinine: Coding = {
  code: 'ug/mg{creat}',
  display: 'microgram per milligram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerMinute: Coding = {
  code: 'ug/min',
  display: 'microgram per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerMilliliter: Coding = {
  code: 'ug/mL',
  display: 'microgram per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerMilliliterClass: Coding = {
  code: 'ug/mL{class}',
  display: 'microgram per milliliter class',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerMilliliterEquivalent: Coding = {
  code: 'ug/mL{eqv}',
  display: 'microgram per milliliter equivalent',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramMilliliterFEU: Coding = {
  code: 'ug/mL{FEU}',
  display: 'microgram / milliliter FEU',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerMillimole: Coding = {
  code: 'ug/mmol',
  display: 'microgram per millimole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerMillimoleOfCreatinine: Coding = {
  code: 'ug/mmol{creat}',
  display: 'microgram per millimole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramPerNanogram: Coding = {
  code: 'ug/ng',
  display: 'microgram per nanogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramFibrinogenEquivalentUnitPerMilliliter: Coding = {
  code: 'ug{FEU}/mL',
  display: 'microgram fibrinogen equivalent unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrogramT4Deciliter: Coding = {
  code: 'ug{T4}/dL',
  display: 'microgram T4 / deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Microkatal: Coding = {
  code: 'ukat',
  display: 'microkatal',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Microliter: Coding = {
  code: 'uL',
  display: 'microliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicroliterPer2Hour: Coding = {
  code: 'uL/(2.h)',
  display: 'microliter per 2 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicroliterPerHour: Coding = {
  code: 'uL/h',
  display: 'microliter per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicrometerPerSecond: Coding = {
  code: 'um/s',
  display: 'micrometer per second',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Micromole: Coding = {
  code: 'umol',
  display: 'micromole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePer2Hour: Coding = {
  code: 'umol/(2.h)',
  display: 'micromole per 2 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePer24Hour: Coding = {
  code: 'umol/(24.h)',
  display: 'micromole per 24 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePer8Hour: Coding = {
  code: 'umol/(8.h)',
  display: 'micromole per 8 hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerDay: Coding = {
  code: 'umol/d',
  display: 'micromole per day',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerDeciliter: Coding = {
  code: 'umol/dL',
  display: 'micromole per deciliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerDeciliterOfGlomerularFiltrate: Coding = {
  code: 'umol/dL{GF}',
  display: 'micromole per deciliter of glomerular filtrate',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerGram: Coding = {
  code: 'umol/g',
  display: 'micromole per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromoleGramCre: Coding = {
  code: 'umol/g{Cre}',
  display: 'micromole / gram Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerGramOfCreatinine: Coding = {
  code: 'umol/g{creat}',
  display: 'micromole per gram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerGramOfHemoglobin: Coding = {
  code: 'umol/g{Hb}',
  display: 'micromole per gram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerGramOfHemoglobin_2: Coding = {
  code: 'umol/g{hemoglobin}',
  display: 'micromole per gram of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromoleGramHgb: Coding = {
  code: 'umol/g{Hgb}',
  display: 'micromole / gram Hgb',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerHour: Coding = {
  code: 'umol/h',
  display: 'micromole per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromoleHourGram: Coding = {
  code: 'umol/h/g',
  display: 'micromole / hour / gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerHourPerLiter: Coding = {
  code: 'umol/h/L',
  display: 'micromole per hour per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerHourPerMilligramOfProtein: Coding = {
  code: 'umol/h/mg{protein}',
  display: 'micromole per hour per milligram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerKilogram: Coding = {
  code: 'umol/kg',
  display: 'micromole per kilogram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerKilogramOfFeces: Coding = {
  code: 'umol/kg{feces}',
  display: 'micromole per kilogram of feces',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerLiter: Coding = {
  code: 'umol/L',
  display: 'micromole per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerLiterPerHour: Coding = {
  code: 'umol/L/h',
  display: 'micromole per liter per hour',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerLiterOfRedBloodCells: Coding = {
  code: 'umol/L{rbc}',
  display: 'micromole per liter of red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerLiterOfRedBloodCells_2: Coding = {
  code: 'umol/L{RBCs}',
  display: 'micromole per liter of red blood cells',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromoleMeter: Coding = {
  code: 'umol/m',
  display: 'micromole / meter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMilligram: Coding = {
  code: 'umol/mg',
  display: 'micromole per milligram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromoleMilligramCre: Coding = {
  code: 'umol/mg{Cre}',
  display: 'micromole / milligram Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMilligramOfCreatinine: Coding = {
  code: 'umol/mg{creat}',
  display: 'micromole per milligram of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMinute: Coding = {
  code: 'umol/min',
  display: 'micromole per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMinutePerGram: Coding = {
  code: 'umol/min/g',
  display: 'micromole per minute per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMinutePerGramOfMucosa: Coding = {
  code: 'umol/min/g{mucosa}',
  display: 'micromole per minute per gram of mucosa',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromoleMinuteGramProt: Coding = {
  code: 'umol/min/g{prot}',
  display: 'micromole / minute / gram prot',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMinutePerGramOfProtein: Coding = {
  code: 'umol/min/g{protein}',
  display: 'micromole per minute per gram of protein',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMinutePerLiter: Coding = {
  code: 'umol/min/L',
  display: 'micromole per minute per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMilliliter: Coding = {
  code: 'umol/mL',
  display: 'micromole per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMilliliterPerMinute: Coding = {
  code: 'umol/mL/min',
  display: 'micromole per milliliter per minute',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMillimole: Coding = {
  code: 'umol/mmol',
  display: 'micromole per millimole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMillimoleOfCreatinine: Coding = {
  code: 'umol/mmol{creat}',
  display: 'micromole per millimole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMole: Coding = {
  code: 'umol/mol',
  display: 'micromole per mole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromoleMoleCre: Coding = {
  code: 'umol/mol{Cre}',
  display: 'micromole / mole Cre',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMoleOfCreatinine: Coding = {
  code: 'umol/mol{creat}',
  display: 'micromole per mole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMoleOfHemoglobin: Coding = {
  code: 'umol/mol{Hb}',
  display: 'micromole per mole of hemoglobin',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMicromole: Coding = {
  code: 'umol/umol',
  display: 'micromole per micromole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromolePerMicromoleOfCreatinine: Coding = {
  code: 'umol/umol{creat}',
  display: 'micromole per micromole of creatinine',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicromoleBoneCollagenEquivalentPerMole: Coding = {
  code: 'umol{BCE}/mol',
  display: 'micromole bone collagen equivalent per mole',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicroOhm: Coding = {
  code: 'uOhm',
  display: 'microOhm',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Microsecond: Coding = {
  code: 'us',
  display: 'microsecond',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicroUnit: Coding = {
  code: 'uU',
  display: 'microUnit',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicroEnzymeUnitPerGram: Coding = {
  code: 'uU/g',
  display: 'micro enzyme unit per gram',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicroEnzymeUnitPerLiter: Coding = {
  code: 'uU/L',
  display: 'micro enzyme unit per liter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_MicroEnzymeUnitPerMilliliter: Coding = {
  code: 'uU/mL',
  display: 'micro enzyme unit per milliliter',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Microvolt: Coding = {
  code: 'uV',
  display: 'microvolt',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Volt: Coding = {
  code: 'V',
  display: 'volt',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Watt: Coding = {
  code: 'W',
  display: 'Watt',
  system: 'http://unitsofmeasure.org',
};
const UcumCommon_Weber: Coding = {
  code: 'Wb',
  display: 'Weber',
  system: 'http://unitsofmeasure.org',
};
/**
 * Commonly encountered UCUM units (for purposes of helping populate look ups.
 */
export const UcumCommon = {
  Minute: UcumCommon_Minute,
  Second: UcumCommon_Second,
  Percent: UcumCommon_Percent,
  Percent100WBC: UcumCommon_Percent100WBC,
  PercentOfSlope: UcumCommon_PercentOfSlope,
  Percent0to3Hours: UcumCommon_Percent0to3Hours,
  PercentAbnormal: UcumCommon_PercentAbnormal,
  PercentAbnormal_2: UcumCommon_PercentAbnormal_2,
  PercentActivity: UcumCommon_PercentActivity,
  PercentActivity_2: UcumCommon_PercentActivity_2,
  PercentAggregation: UcumCommon_PercentAggregation,
  PercentAt60Minute: UcumCommon_PercentAt60Minute,
  PercentOfBacteria: UcumCommon_PercentOfBacteria,
  PercentBasalActivity: UcumCommon_PercentBasalActivity,
  PercentBasalActivity_2: UcumCommon_PercentBasalActivity_2,
  PercentOfBaseline: UcumCommon_PercentOfBaseline,
  PercentBinding: UcumCommon_PercentBinding,
  PercentBinding_2: UcumCommon_PercentBinding_2,
  PercentBlockade: UcumCommon_PercentBlockade,
  PercentBlockade_2: UcumCommon_PercentBlockade_2,
  PercentBlocked: UcumCommon_PercentBlocked,
  PercentBound: UcumCommon_PercentBound,
  PercentBound_2: UcumCommon_PercentBound_2,
  PercentBreakdown: UcumCommon_PercentBreakdown,
  PercentCarboxyhemoglobin: UcumCommon_PercentCarboxyhemoglobin,
  PercentOfCells: UcumCommon_PercentOfCells,
  PercentConversion: UcumCommon_PercentConversion,
  PercentCound: UcumCommon_PercentCound,
  PercentDeficient: UcumCommon_PercentDeficient,
  PercentDose: UcumCommon_PercentDose,
  PercentEosSeen: UcumCommon_PercentEosSeen,
  PercentExcretion: UcumCommon_PercentExcretion,
  PercentExcretion_2: UcumCommon_PercentExcretion_2,
  PercentFat: UcumCommon_PercentFat,
  PercentFetalErythrocytes: UcumCommon_PercentFetalErythrocytes,
  PercentHemoglobin: UcumCommon_PercentHemoglobin,
  PercentHemoglobin_2: UcumCommon_PercentHemoglobin_2,
  PercentHemoglobinA1C: UcumCommon_PercentHemoglobinA1C,
  PercentHemoglobinSaturation: UcumCommon_PercentHemoglobinSaturation,
  PercentHemolysis: UcumCommon_PercentHemolysis,
  PercentHemolysis_2: UcumCommon_PercentHemolysis_2,
  PercentHumanResponse: UcumCommon_PercentHumanResponse,
  PercentIndex: UcumCommon_PercentIndex,
  PercentIndex_2: UcumCommon_PercentIndex_2,
  PercentInhibition: UcumCommon_PercentInhibition,
  PercentInhibition_2: UcumCommon_PercentInhibition_2,
  PercentLive: UcumCommon_PercentLive,
  PercentLossOfAcetylcholineReceptor: UcumCommon_PercentLossOfAcetylcholineReceptor,
  PercentLoss: UcumCommon_PercentLoss,
  PercentLysis: UcumCommon_PercentLysis,
  PercentNegativeControl: UcumCommon_PercentNegativeControl,
  PercentNormalPooledPlasma: UcumCommon_PercentNormalPooledPlasma,
  PercentNormal: UcumCommon_PercentNormal,
  PercentNormal_2: UcumCommon_PercentNormal_2,
  PercentNormalControl: UcumCommon_PercentNormalControl,
  PercentNormalPooledPlasma_2: UcumCommon_PercentNormalPooledPlasma_2,
  PercentOfAvailable: UcumCommon_PercentOfAvailable,
  PercentOfBacteria_2: UcumCommon_PercentOfBacteria_2,
  PercentOfLymphocytes: UcumCommon_PercentOfLymphocytes,
  PercentOfWBCs: UcumCommon_PercentOfWBCs,
  PercentOxygen: UcumCommon_PercentOxygen,
  PercentPenetration: UcumCommon_PercentPenetration,
  PercentPositive: UcumCommon_PercentPositive,
  PercentPositive_2: UcumCommon_PercentPositive_2,
  PercentPrecipitate: UcumCommon_PercentPrecipitate,
  PercentOfRedBloodCells: UcumCommon_PercentOfRedBloodCells,
  PercentReactive: UcumCommon_PercentReactive,
  PercentReactivity: UcumCommon_PercentReactivity,
  PercentRecovery: UcumCommon_PercentRecovery,
  PercentReference: UcumCommon_PercentReference,
  RelativePercent: UcumCommon_RelativePercent,
  PercentResidual: UcumCommon_PercentResidual,
  PercentResponse: UcumCommon_PercentResponse,
  PercentRisk: UcumCommon_PercentRisk,
  PercentSaturation: UcumCommon_PercentSaturation,
  PercentSpermMotility: UcumCommon_PercentSpermMotility,
  PercentTotal: UcumCommon_PercentTotal,
  PercentTotal_2: UcumCommon_PercentTotal_2,
  PercentTotalProtein: UcumCommon_PercentTotalProtein,
  PercentTotQuoteCholesterol: UcumCommon_PercentTotQuoteCholesterol,
  PercentTotQuoteHgb: UcumCommon_PercentTotQuoteHgb,
  PercentUptake: UcumCommon_PercentUptake,
  PercentUptake_2: UcumCommon_PercentUptake_2,
  PercentViable: UcumCommon_PercentViable,
  VolumePercent: UcumCommon_VolumePercent,
  PercentOfWhiteBloodCells: UcumCommon_PercentOfWhiteBloodCells,
  PercentWeightToWeight: UcumCommon_PercentWeightToWeight,
  Per12AsteriskHour: UcumCommon_Per12AsteriskHour,
  PerArbitraryUnit: UcumCommon_PerArbitraryUnit,
  PerHighPowerField: UcumCommon_PerHighPowerField,
  PerInternationalUnit: UcumCommon_PerInternationalUnit,
  PerInternationalUnit_2: UcumCommon_PerInternationalUnit_2,
  PerLowPowerField: UcumCommon_PerLowPowerField,
  PerEntity: UcumCommon_PerEntity,
  PerEntity_2: UcumCommon_PerEntity_2,
  PerOif: UcumCommon_PerOif,
  PerOilImmersionField: UcumCommon_PerOilImmersionField,
  PerSpecimen: UcumCommon_PerSpecimen,
  PerTot: UcumCommon_PerTot,
  PerTenGiga: UcumCommon_PerTenGiga,
  PerTrillion: UcumCommon_PerTrillion,
  PerTrillionRedBloodCells: UcumCommon_PerTrillionRedBloodCells,
  PerTrillionRedBloodCells_2: UcumCommon_PerTrillionRedBloodCells_2,
  PerThousand: UcumCommon_PerThousand,
  PerThousandRedBloodCells: UcumCommon_PerThousandRedBloodCells,
  Per10ThousandRedBloodCells: UcumCommon_Per10ThousandRedBloodCells,
  PerMillion: UcumCommon_PerMillion,
  PerBillion: UcumCommon_PerBillion,
  Per100: UcumCommon_Per100,
  Per100Cells: UcumCommon_Per100Cells,
  Per100Neutrophils: UcumCommon_Per100Neutrophils,
  Per100Spermatozoa: UcumCommon_Per100Spermatozoa,
  Per100Spermatozoa_2: UcumCommon_Per100Spermatozoa_2,
  Per100WBC: UcumCommon_Per100WBC,
  Per100WBC_2: UcumCommon_Per100WBC_2,
  PerYear: UcumCommon_PerYear,
  PerCentimeterOfWater: UcumCommon_PerCentimeterOfWater,
  PerDay: UcumCommon_PerDay,
  PerDeciliter: UcumCommon_PerDeciliter,
  PerGram: UcumCommon_PerGram,
  PerGramCreat: UcumCommon_PerGramCreat,
  PerGramOfHemoglobin: UcumCommon_PerGramOfHemoglobin,
  PerGramHgb: UcumCommon_PerGramHgb,
  PerGramHemoglobin: UcumCommon_PerGramHemoglobin,
  PerGramOfTotalNitrogen: UcumCommon_PerGramOfTotalNitrogen,
  PerGramOfTotalProtein: UcumCommon_PerGramOfTotalProtein,
  PerGramTotQuoteNit: UcumCommon_PerGramTotQuoteNit,
  PerGramTotQuoteProt: UcumCommon_PerGramTotQuoteProt,
  PerGramOfWetTissue: UcumCommon_PerGramOfWetTissue,
  PerGramWetQuoteTis: UcumCommon_PerGramWetQuoteTis,
  PerHour: UcumCommon_PerHour,
  PerKilogram: UcumCommon_PerKilogram,
  PerKilogramOfBodyWeight: UcumCommon_PerKilogramOfBodyWeight,
  PerKilogramBodyWt: UcumCommon_PerKilogramBodyWt,
  PerLiter: UcumCommon_PerLiter,
  PerSquareMeter: UcumCommon_PerSquareMeter,
  PerCubicMeter: UcumCommon_PerCubicMeter,
  PerMilligram: UcumCommon_PerMilligram,
  PerMinute: UcumCommon_PerMinute,
  PerMilliliter: UcumCommon_PerMilliliter,
  PerMillimeter: UcumCommon_PerMillimeter,
  PerCubicMillimeter: UcumCommon_PerCubicMillimeter,
  PerMillimole: UcumCommon_PerMillimole,
  PerMillimoleOfCreatinine: UcumCommon_PerMillimoleOfCreatinine,
  PerMonth: UcumCommon_PerMonth,
  PerSecond: UcumCommon_PerSecond,
  PerEnzymeUnit: UcumCommon_PerEnzymeUnit,
  PerMicrogram: UcumCommon_PerMicrogram,
  PerMicroliter: UcumCommon_PerMicroliter,
  PerWeek: UcumCommon_PerWeek,
  Acre: UcumCommon_Acre,
  Acre_2: UcumCommon_Acre_2,
  AmbA1Units: UcumCommon_AmbA1Units,
  IgAAnticardiolipinUnit: UcumCommon_IgAAnticardiolipinUnit,
  IgAAnticardiolipinUnitPerMilliliter: UcumCommon_IgAAnticardiolipinUnitPerMilliliter,
  ArbitraryUnit: UcumCommon_ArbitraryUnit,
  ArbitaryUnitLiter: UcumCommon_ArbitaryUnitLiter,
  ArbitraryUnitPerMilliliter: UcumCommon_ArbitraryUnitPerMilliliter,
  AllergyUnit: UcumCommon_AllergyUnit,
  BioequivalentAllergenUnit: UcumCommon_BioequivalentAllergenUnit,
  Barrel: UcumCommon_Barrel,
  BodanskyUnit: UcumCommon_BodanskyUnit,
  BethesdaUnit: UcumCommon_BethesdaUnit,
  BoardFoot: UcumCommon_BoardFoot,
  BritishThermalUnit: UcumCommon_BritishThermalUnit,
  BritishThermalUnitAt39DegreesF: UcumCommon_BritishThermalUnitAt39DegreesF,
  BritishThermalUnitAt59DegreesF: UcumCommon_BritishThermalUnitAt59DegreesF,
  BritishThermalUnitAt60DegreesF: UcumCommon_BritishThermalUnitAt60DegreesF,
  InternationalTableBritishThermalUnit: UcumCommon_InternationalTableBritishThermalUnit,
  MeanBritishThermalUnit: UcumCommon_MeanBritishThermalUnit,
  ThermochemicalBritishThermalUnit: UcumCommon_ThermochemicalBritishThermalUnit,
  Bushel: UcumCommon_Bushel,
  Bushel_2: UcumCommon_Bushel_2,
  VelocityOfLight: UcumCommon_VelocityOfLight,
  NutritionLabelCalories: UcumCommon_NutritionLabelCalories,
  CaratOfGoldAlloys: UcumCommon_CaratOfGoldAlloys,
  MetricCarat: UcumCommon_MetricCarat,
  CELLCULTUREINFECTIOUSDOSE50Percent: UcumCommon_CELLCULTUREINFECTIOUSDOSE50Percent,
  CubicFoot: UcumCommon_CubicFoot,
  ColonyFormingUnit: UcumCommon_ColonyFormingUnit,
  ColonyFormingUnitPerLiter: UcumCommon_ColonyFormingUnitPerLiter,
  ColonyFormingUnitPerMilliliter: UcumCommon_ColonyFormingUnitPerMilliliter,
  FrenchCatheterGauge: UcumCommon_FrenchCatheterGauge,
  GunterQuoteSChain: UcumCommon_GunterQuoteSChain,
  GunterQuoteSChainSurveyorQuoteSChain: UcumCommon_GunterQuoteSChainSurveyorQuoteSChain,
  CiceroDidotQuoteSPica: UcumCommon_CiceroDidotQuoteSPica,
  CubicInch: UcumCommon_CubicInch,
  CircularMil: UcumCommon_CircularMil,
  Cord: UcumCommon_Cord,
  Cord_2: UcumCommon_Cord_2,
  Cup: UcumCommon_Cup,
  CubicYard: UcumCommon_CubicYard,
  DANTIGENUNITS: UcumCommon_DANTIGENUNITS,
  NONE: UcumBodytemp_NONE,
  DidotDidotQuoteSPoint: UcumCommon_DidotDidotQuoteSPoint,
  Diopter: UcumCommon_Diopter,
  DryPint: UcumCommon_DryPint,
  DryQuart: UcumCommon_DryQuart,
  DramDrachm: UcumCommon_DramDrachm,
  DramUSAndBritish: UcumCommon_DramUSAndBritish,
  Drop: UcumCommon_Drop,
  DropHPF: UcumCommon_DropHPF,
  DropHour: UcumCommon_DropHour,
  DropMinute: UcumCommon_DropMinute,
  DropMilliliter: UcumCommon_DropMilliliter,
  DropSecond: UcumCommon_DropSecond,
  DyeUnit: UcumCommon_DyeUnit,
  ElementaryCharge: UcumCommon_ElementaryCharge,
  PermittivityOfVacuum: UcumCommon_PermittivityOfVacuum,
  FluidDram: UcumCommon_FluidDram,
  FluidDram_2: UcumCommon_FluidDram_2,
  FOCUSFORMINGUNITS: UcumCommon_FOCUSFORMINGUNITS,
  FluidOunce: UcumCommon_FluidOunce,
  FluidOunce_2: UcumCommon_FluidOunce_2,
  Foot: UcumCommon_Foot,
  Feet: UcumCommon_Feet,
  Foot_2: UcumCommon_Foot_2,
  Fathom: UcumCommon_Fathom,
  Fathom_2: UcumCommon_Fathom_2,
  Fathom_3: UcumCommon_Fathom_3,
  Furlong: UcumCommon_Furlong,
  NewtonianConstantOfGravitation: UcumCommon_NewtonianConstantOfGravitation,
  Gallon: UcumCommon_Gallon,
  QueenAnneQuoteSWineGallon: UcumCommon_QueenAnneQuoteSWineGallon,
  HistoricalWinchesterGallon: UcumCommon_HistoricalWinchesterGallon,
  Gill: UcumCommon_Gill,
  Gill_2: UcumCommon_Gill_2,
  IgGAnticardiolipinUnit: UcumCommon_IgGAnticardiolipinUnit,
  IgGAnticardiolipinUnitPerMilliliterAsteriskAsterisk: UcumCommon_IgGAnticardiolipinUnitPerMilliliterAsteriskAsterisk,
  Grain: UcumCommon_Grain,
  PlanckConstant: UcumCommon_PlanckConstant,
  Hand: UcumCommon_Hand,
  HounsfieldUnit: UcumCommon_HounsfieldUnit,
  Horsepower: UcumCommon_Horsepower,
  HOMEOPATHICPOTENCYOFCENTESIMALSERIES: UcumCommon_HOMEOPATHICPOTENCYOFCENTESIMALSERIES,
  HOMEOPATHICPOTENCYOFMILLESIMALSERIES: UcumCommon_HOMEOPATHICPOTENCYOFMILLESIMALSERIES,
  HOMEOPATHICPOTENCYOFQUINTAMILLESIMALSERIES: UcumCommon_HOMEOPATHICPOTENCYOFQUINTAMILLESIMALSERIES,
  HOMEOPATHICPOTENCYOFDECIMALSERIES: UcumCommon_HOMEOPATHICPOTENCYOFDECIMALSERIES,
  HighPowerField: UcumCommon_HighPowerField,
  Inch: UcumCommon_Inch,
  NONE_2: UcumBodylength_NONE,
  InchInternationalOfWater: UcumCommon_InchInternationalOfWater,
  InchOfMercuryColumn: UcumCommon_InchOfMercuryColumn,
  Inch_2: UcumCommon_Inch_2,
  InternationalUnit: UcumCommon_InternationalUnit,
  InternationalUnit_2: UcumCommon_InternationalUnit_2,
  InternationalUnitPer2Hour: UcumCommon_InternationalUnitPer2Hour,
  InternationalUnitPer24Hour: UcumCommon_InternationalUnitPer24Hour,
  InternationalUnitPerBillionRedBloodCells: UcumCommon_InternationalUnitPerBillionRedBloodCells,
  InternationalUnitPerDay: UcumCommon_InternationalUnitPerDay,
  InternationalUnitsPerDeciLiter: UcumCommon_InternationalUnitsPerDeciLiter,
  InternationalUnitPerDeciliter: UcumCommon_InternationalUnitPerDeciliter,
  InternationalUnitsPerGram: UcumCommon_InternationalUnitsPerGram,
  InternationalUnitPerGram: UcumCommon_InternationalUnitPerGram,
  InternationalUnitPerGramOfHemoglobin: UcumCommon_InternationalUnitPerGramOfHemoglobin,
  InternationalUnitGramHgb: UcumCommon_InternationalUnitGramHgb,
  InternationalUnitPerHour: UcumCommon_InternationalUnitPerHour,
  InternationalUnitsPerKilogram: UcumCommon_InternationalUnitsPerKilogram,
  InternationalUnitPerKilogram: UcumCommon_InternationalUnitPerKilogram,
  InternationalUnitPerKilogramPerDay: UcumCommon_InternationalUnitPerKilogramPerDay,
  InternationalUnitsPerLiter: UcumCommon_InternationalUnitsPerLiter,
  InternationalUnitPerLiter: UcumCommon_InternationalUnitPerLiter,
  InternationalUnitPerLiterAt37DegreesCelsius: UcumCommon_InternationalUnitPerLiterAt37DegreesCelsius,
  InternationalUnitPerMilligramOfCreatinine: UcumCommon_InternationalUnitPerMilligramOfCreatinine,
  InternationalUnitPerMinute: UcumCommon_InternationalUnitPerMinute,
  InternationalUnitsPerMilliLiter: UcumCommon_InternationalUnitsPerMilliLiter,
  InternationalUnitPerMilliliter: UcumCommon_InternationalUnitPerMilliliter,
  BoltzmannConstant: UcumCommon_BoltzmannConstant,
  KingArmstrongUnit: UcumCommon_KingArmstrongUnit,
  Knot: UcumCommon_Knot,
  Knot_2: UcumCommon_Knot_2,
  KunkelUnit: UcumCommon_KunkelUnit,
  Pound: UcumCommon_Pound,
  NONE_3: UcumBodyweight_NONE,
  Pound_2: UcumCommon_Pound_2,
  PoundForce: UcumCommon_PoundForce,
  LongHunderdweightBritishHundredweight: UcumCommon_LongHunderdweightBritishHundredweight,
  LIMITOFFLOCCULATION: UcumCommon_LIMITOFFLOCCULATION,
  LigneFrenchLine: UcumCommon_LigneFrenchLine,
  LinkForGunterQuoteSChain: UcumCommon_LinkForGunterQuoteSChain,
  LinkForGunterQuoteSChain_2: UcumCommon_LinkForGunterQuoteSChain_2,
  Line: UcumCommon_Line,
  LowPowerField: UcumCommon_LowPowerField,
  LongTonBritishTon: UcumCommon_LongTonBritishTon,
  LightYear: UcumCommon_LightYear,
  ElectronMass: UcumCommon_ElectronMass,
  ProtonMass: UcumCommon_ProtonMass,
  MacLaganUnit: UcumCommon_MacLaganUnit,
  Mesh: UcumCommon_Mesh,
  MetabolicEquivalent: UcumCommon_MetabolicEquivalent,
  Mile: UcumCommon_Mile,
  StatuteMile: UcumCommon_StatuteMile,
  Mile_2: UcumCommon_Mile_2,
  Mil: UcumCommon_Mil,
  Mil_2: UcumCommon_Mil_2,
  Minim: UcumCommon_Minim,
  Minim_2: UcumCommon_Minim_2,
  IgMAnticardiolipinUnit: UcumCommon_IgMAnticardiolipinUnit,
  IgMAnticardiolipinUnitPerMilliliterAsteriskAsterisk: UcumCommon_IgMAnticardiolipinUnitPerMilliliterAsteriskAsterisk,
  PermeabilityOfVacuum: UcumCommon_PermeabilityOfVacuum,
  NauticalMile: UcumCommon_NauticalMile,
  NauticalMile_2: UcumCommon_NauticalMile_2,
  OunceUSAndBritish: UcumCommon_OunceUSAndBritish,
  OunceUSAndBritish_2: UcumCommon_OunceUSAndBritish_2,
  Ounce: UcumCommon_Ounce,
  Pace: UcumCommon_Pace,
  Pica: UcumCommon_Pica,
  PrinterQuoteSPica: UcumCommon_PrinterQuoteSPica,
  PrismDiopter: UcumCommon_PrismDiopter,
  PLAQUEFORMINGUNITS: UcumCommon_PLAQUEFORMINGUNITS,
  PH: UcumCommon_PH,
  TheNumberPi: UcumCommon_TheNumberPi,
  TheNumberPiAsteriskRadianMinute: UcumCommon_TheNumberPiAsteriskRadianMinute,
  PiedFrenchFoot: UcumCommon_PiedFrenchFoot,
  Peck: UcumCommon_Peck,
  Peck_2: UcumCommon_Peck_2,
  Point: UcumCommon_Point,
  PrinterQuoteSPoint: UcumCommon_PrinterQuoteSPoint,
  PROTEINNITROGENUNITS: UcumCommon_PROTEINNITROGENUNITS,
  PouceFrenchInch: UcumCommon_PouceFrenchInch,
  PartPerBillion: UcumCommon_PartPerBillion,
  PartPerMillion: UcumCommon_PartPerMillion,
  PartPerMillionInVolumePerVolume: UcumCommon_PartPerMillionInVolumePerVolume,
  PartsPerThousand: UcumCommon_PartsPerThousand,
  PartPerTrillion: UcumCommon_PartPerTrillion,
  PeripheralVascularResistanceUnit: UcumCommon_PeripheralVascularResistanceUnit,
  PoundPerSquareInch: UcumCommon_PoundPerSquareInch,
  Pint: UcumCommon_Pint,
  Pint_2: UcumCommon_Pint_2,
  Pennyweight: UcumCommon_Pennyweight,
  Quart: UcumCommon_Quart,
  Quart_2: UcumCommon_Quart_2,
  RamdenQuoteSChainEngineerQuoteSChain: UcumCommon_RamdenQuoteSChainEngineerQuoteSChain,
  Rod: UcumCommon_Rod,
  Rod_2: UcumCommon_Rod_2,
  LinkForRamdenQuoteSChain: UcumCommon_LinkForRamdenQuoteSChain,
  SvedbergUnit: UcumCommon_SvedbergUnit,
  Scruple: UcumCommon_Scruple,
  Section: UcumCommon_Section,
  ShortHundredweightUSHundredweight: UcumCommon_ShortHundredweightUSHundredweight,
  SquareFootInternational: UcumCommon_SquareFootInternational,
  SquareInchInternational: UcumCommon_SquareInchInternational,
  SomogyiUnit: UcumCommon_SomogyiUnit,
  SquareMile: UcumCommon_SquareMile,
  Smoot: UcumCommon_Smoot,
  SquareRod: UcumCommon_SquareRod,
  ShortTonUSTon: UcumCommon_ShortTonUSTon,
  StoneBritishStone: UcumCommon_StoneBritishStone,
  SquareYard: UcumCommon_SquareYard,
  TablespoonUS: UcumCommon_TablespoonUS,
  TuberculinUnit: UcumCommon_TuberculinUnit,
  TISSUECULTUREINFECTIOUSDOSE50Percent: UcumCommon_TISSUECULTUREINFECTIOUSDOSE50Percent,
  ToddUnit: UcumCommon_ToddUnit,
  Teaspoon: UcumCommon_Teaspoon,
  Township: UcumCommon_Township,
  UNITEDSTATESPHARMACOPEIAUNIT: UcumCommon_UNITEDSTATESPHARMACOPEIAUNIT,
  Yard: UcumCommon_Yard,
  Yard_2: UcumCommon_Yard_2,
  Yard_3: UcumCommon_Yard_3,
  NumberOfCalculi: UcumCommon_NumberOfCalculi,
  NumberOfDonorInformativeMarkers: UcumCommon_NumberOfDonorInformativeMarkers,
  NumberOfFetuses: UcumCommon_NumberOfFetuses,
  NumberOfInformativeMarkers: UcumCommon_NumberOfInformativeMarkers,
  Number: UcumCommon_Number,
  NumberPerHighPowerField: UcumCommon_NumberPerHighPowerField,
  NumberPerLowPowerField: UcumCommon_NumberPerLowPowerField,
  NumberPerLiter: UcumCommon_NumberPerLiter,
  NumberPerMinute: UcumCommon_NumberPerMinute,
  NumberPerMilliliter: UcumCommon_NumberPerMilliliter,
  NumberPerMicroliter: UcumCommon_NumberPerMicroliter,
  VAL2Or3TimesDay: UcumCommon_VAL2Or3TimesDay,
  VAL3TimesDay: UcumCommon_VAL3TimesDay,
  VAL4TimesDay: UcumCommon_VAL4TimesDay,
  VAL5TimesDay: UcumCommon_VAL5TimesDay,
  Absorbance: UcumCommon_Absorbance,
  AbsorbanceQuoteU: UcumCommon_AbsorbanceQuoteU,
  AbsorbanceQuoteUMilliliter: UcumCommon_AbsorbanceQuoteUMilliliter,
  Activity: UcumCommon_Activity,
  ActivityCoefficient: UcumCommon_ActivityCoefficient,
  AHFQuoteU: UcumCommon_AHFQuoteU,
  AntibodyResponseQuoteU: UcumCommon_AntibodyResponseQuoteU,
  Applicator: UcumCommon_Applicator,
  IgAAntiphosphatidylserineUnit: UcumCommon_IgAAntiphosphatidylserineUnit,
  AspirinResponseUnit: UcumCommon_AspirinResponseUnit,
  AspirinReactionQuoteU: UcumCommon_AspirinReactionQuoteU,
  Bead: UcumCommon_Bead,
  HeartBeatsPerMinute: UcumCommon_HeartBeatsPerMinute,
  BeatsMinute: UcumCommon_BeatsMinute,
  BindingIndex: UcumCommon_BindingIndex,
  Bottle: UcumCommon_Bottle,
  BowlsDay: UcumCommon_BowlsDay,
  BreathsMinute: UcumCommon_BreathsMinute,
  ComplementActivityEnzymeUnit: UcumCommon_ComplementActivityEnzymeUnit,
  CAGTrinucleotideRepeats: UcumCommon_CAGTrinucleotideRepeats,
  CagRepeat: UcumCommon_CagRepeat,
  Can: UcumCommon_Can,
  CansWeek: UcumCommon_CansWeek,
  Capsule: UcumCommon_Capsule,
  Cell: UcumCommon_Cell,
  Cells: UcumCommon_Cells,
  CellsPerHighPowerField: UcumCommon_CellsPerHighPowerField,
  CellsMilliliter: UcumCommon_CellsMilliliter,
  CellsPerMicroliter: UcumCommon_CellsPerMicroliter,
  CellsPerMicroLiter: UcumCommon_CellsPerMicroLiter,
  CfTiter: UcumCommon_CfTiter,
  Cfu: UcumCommon_Cfu,
  CfuMilliliter: UcumCommon_CfuMilliliter,
  CGGRepeats: UcumCommon_CGGRepeats,
  CGG: UcumCommon_CGG,
  ComplementCH100Unit: UcumCommon_ComplementCH100Unit,
  ClockTime: UcumCommon_ClockTime,
  ClockTime_2: UcumCommon_ClockTime_2,
  ComplementActivityEnzymeQuoteU: UcumCommon_ComplementActivityEnzymeQuoteU,
  ComplementCH100QuoteU: UcumCommon_ComplementCH100QuoteU,
  ComplementCH50QuoteU: UcumCommon_ComplementCH50QuoteU,
  Copies: UcumCommon_Copies,
  CopiesPerMilliliter: UcumCommon_CopiesPerMilliliter,
  CopiesPerMilliLiter: UcumCommon_CopiesPerMilliLiter,
  CopiesPerMicrogram: UcumCommon_CopiesPerMicrogram,
  CopiesMicroliter: UcumCommon_CopiesMicroliter,
  Count: UcumCommon_Count,
  Count_2: UcumCommon_Count_2,
  CountsMinute: UcumCommon_CountsMinute,
  CountsPerMinute: UcumCommon_CountsPerMinute,
  CountsPerMinutePerThousandCells: UcumCommon_CountsPerMinutePerThousandCells,
  CrossingThreshold: UcumCommon_CrossingThreshold,
  Dalton: UcumCommon_Dalton,
  DdTiter: UcumCommon_DdTiter,
  ChangeInDeltaOpticalDensity: UcumCommon_ChangeInDeltaOpticalDensity,
  DeltaOpticalDensity: UcumCommon_DeltaOpticalDensity,
  Dilution: UcumCommon_Dilution,
  Dilution_2: UcumCommon_Dilution_2,
  DisintegrationsMinute: UcumCommon_DisintegrationsMinute,
  Dose: UcumCommon_Dose,
  DrinksDay: UcumCommon_DrinksDay,
  Each: UcumCommon_Each,
  EhrlichUnitsPerDeciLiter: UcumCommon_EhrlichUnitsPerDeciLiter,
  EhrlichUnit: UcumCommon_EhrlichUnit,
  EhrlichUnitPer2Hour: UcumCommon_EhrlichUnitPer2Hour,
  EhrlichUnitPer100Gram: UcumCommon_EhrlichUnitPer100Gram,
  EhrlichUnitPerDay: UcumCommon_EhrlichUnitPerDay,
  EhrlichUnitsPerDeciLiterArbitraryConcentrationUnits: UcumCommon_EhrlichUnitsPerDeciLiterArbitraryConcentrationUnits,
  EhrilichUnitPerDeciliter: UcumCommon_EhrilichUnitPerDeciliter,
  EhrlichQuoteUMilliliter: UcumCommon_EhrlichQuoteUMilliliter,
  EIAIndex: UcumCommon_EIAIndex,
  EIATiter: UcumCommon_EIATiter,
  EIAIndex_2: UcumCommon_EIAIndex_2,
  EIATiter_2: UcumCommon_EIATiter_2,
  EIAUnit: UcumCommon_EIAUnit,
  EIAUnitPerEnzymeUnit: UcumCommon_EIAUnitPerEnzymeUnit,
  ElisaUnitsPerMilliLiter: UcumCommon_ElisaUnitsPerMilliLiter,
  ElisaIndex: UcumCommon_ElisaIndex,
  ELISAUnit: UcumCommon_ELISAUnit,
  ElisaUnitsPerMilliLiterArbitraryConcentrationUnits: UcumCommon_ElisaUnitsPerMilliLiterArbitraryConcentrationUnits,
  ElisaQuoteUMilliliter: UcumCommon_ElisaQuoteUMilliliter,
  ElisaValue: UcumCommon_ElisaValue,
  ErythrocytePerMicroliter: UcumCommon_ErythrocytePerMicroliter,
  EIAValue: UcumCommon_EIAValue,
  Events: UcumCommon_Events,
  FluorescentIntensityUnit: UcumCommon_FluorescentIntensityUnit,
  FluorescenceIntensityQuoteU: UcumCommon_FluorescenceIntensityQuoteU,
  Fraction: UcumCommon_Fraction,
  GAATrinucleotideRepeats: UcumCommon_GAATrinucleotideRepeats,
  GenomesPerMilliliter: UcumCommon_GenomesPerMilliliter,
  GliadinIndexValue: UcumCommon_GliadinIndexValue,
  GlobulesDropsPerHighPowerField: UcumCommon_GlobulesDropsPerHighPowerField,
  GPortionPhospholipids: UcumCommon_GPortionPhospholipids,
  IgGAntiphosphatidylserineUnit: UcumCommon_IgGAntiphosphatidylserineUnit,
  InfluenzaHemagglutinationTiter: UcumCommon_InfluenzaHemagglutinationTiter,
  HaTiter: UcumCommon_HaTiter,
  ImmunofluorescenceAssayIndex: UcumCommon_ImmunofluorescenceAssayIndex,
  ImmunofluorescenceAssayTiter: UcumCommon_ImmunofluorescenceAssayTiter,
  IfaIndex: UcumCommon_IfaIndex,
  IfaTiter: UcumCommon_IfaTiter,
  IgAAntiphosphatidyleserineQuoteU: UcumCommon_IgAAntiphosphatidyleserineQuoteU,
  IgAPhospholipidQuoteU: UcumCommon_IgAPhospholipidQuoteU,
  IgGAntiphosphatidyleserineQuoteU: UcumCommon_IgGAntiphosphatidyleserineQuoteU,
  IgGIndex: UcumCommon_IgGIndex,
  IgMAntiphosphatidyleserineQuoteU: UcumCommon_IgMAntiphosphatidyleserineQuoteU,
  IgMIndex: UcumCommon_IgMIndex,
  ImmuneComplexUnit: UcumCommon_ImmuneComplexUnit,
  ImmuneStatusRatio: UcumCommon_ImmuneStatusRatio,
  Immunity: UcumCommon_Immunity,
  IndexValue: UcumCommon_IndexValue,
  IndexVal: UcumCommon_IndexVal,
  Index: UcumCommon_Index,
  IndexValue_2: UcumCommon_IndexValue_2,
  InhaledTobaccoUseAmountYears: UcumCommon_InhaledTobaccoUseAmountYears,
  InhaledTobaccoUsePacksDay: UcumCommon_InhaledTobaccoUsePacksDay,
  InternationalNormalizedRatio: UcumCommon_InternationalNormalizedRatio,
  INRQuoteUnit: UcumCommon_INRQuoteUnit,
  ImmuneStatusRatio_2: UcumCommon_ImmuneStatusRatio_2,
  JuvenileDiabetesFoundationUnit: UcumCommon_JuvenileDiabetesFoundationUnit,
  JuvenileDiabetesFoundationUnitPerLiter: UcumCommon_JuvenileDiabetesFoundationUnitPerLiter,
  JuvenileDiabetesFoundQuoteU: UcumCommon_JuvenileDiabetesFoundQuoteU,
  KaolinClottingTime: UcumCommon_KaolinClottingTime,
  HOMEOPATHICPOTENCYOFCENTESIMALKORSAKOVIANSERIES: UcumCommon_HOMEOPATHICPOTENCYOFCENTESIMALKORSAKOVIANSERIES,
  KronusUnitPerLiter: UcumCommon_KronusUnitPerLiter,
  KronusUnitPerMilliliter: UcumCommon_KronusUnitPerMilliliter,
  LgCopiesMilliliter: UcumCommon_LgCopiesMilliliter,
  LogBase10CopiesPerMilliliter: UcumCommon_LogBase10CopiesPerMilliliter,
  LogBase10CopiesPerMilliliter_2: UcumCommon_LogBase10CopiesPerMilliliter_2,
  LogBase10InternationalUnit: UcumCommon_LogBase10InternationalUnit,
  LogBase10InternationalUnitPerMilliliter: UcumCommon_LogBase10InternationalUnitPerMilliliter,
  LogBase10InternationalUnitPerMilliliter_2: UcumCommon_LogBase10InternationalUnitPerMilliliter_2,
  LogBase10: UcumCommon_LogBase10,
  LymeIndexValue: UcumCommon_LymeIndexValue,
  LymeIndexValue_2: UcumCommon_LymeIndexValue_2,
  MOM: UcumCommon_MOM,
  MultipleOfTheMedian: UcumCommon_MultipleOfTheMedian,
  Markers: UcumCommon_Markers,
  MinidropPerMinute: UcumCommon_MinidropPerMinute,
  MinidropPerSecond: UcumCommon_MinidropPerSecond,
  Minidrp: UcumCommon_Minidrp,
  MonthDayYear: UcumCommon_MonthDayYear,
  MoleculePerPlatelet: UcumCommon_MoleculePerPlatelet,
  MoleculePlatelet: UcumCommon_MoleculePlatelet,
  MPortionPhospholipids: UcumCommon_MPortionPhospholipids,
  IgMAntiphosphatidylserineUnit: UcumCommon_IgMAntiphosphatidylserineUnit,
  IgMAntiphosphatidylserineUnitPerMilliliter: UcumCommon_IgMAntiphosphatidylserineUnitPerMilliliter,
  MultOfMean: UcumCommon_MultOfMean,
  Mutation: UcumCommon_Mutation,
  NonspecificOunce: UcumCommon_NonspecificOunce,
  Number_2: UcumCommon_Number_2,
  OpticalDensityUnit: UcumCommon_OpticalDensityUnit,
  OnceDay: UcumCommon_OnceDay,
  OpticalDensity: UcumCommon_OpticalDensity,
  OpticalDensityIndex: UcumCommon_OpticalDensityIndex,
  OpticalDensityRatio: UcumCommon_OpticalDensityRatio,
  P2Y12ReactionUnits: UcumCommon_P2Y12ReactionUnits,
  Package: UcumCommon_Package,
  PacksDay: UcumCommon_PacksDay,
  PackYears: UcumCommon_PackYears,
  PanbioUnit: UcumCommon_PanbioUnit,
  Patch: UcumCommon_Patch,
  Percentile: UcumCommon_Percentile,
  Percentile_2: UcumCommon_Percentile_2,
  Phenotype: UcumCommon_Phenotype,
  Pill: UcumCommon_Pill,
  PouchesWeek: UcumCommon_PouchesWeek,
  RadioactiveT3UptakeRatio: UcumCommon_RadioactiveT3UptakeRatio,
  Ratio: UcumCommon_Ratio,
  RedBloodCellCount: UcumCommon_RedBloodCellCount,
  RedBloodCellPerMicroliter: UcumCommon_RedBloodCellPerMicroliter,
  RecTiter: UcumCommon_RecTiter,
  RelativeSaturation: UcumCommon_RelativeSaturation,
  RelativeQuoteU: UcumCommon_RelativeQuoteU,
  RelativeViscosity: UcumCommon_RelativeViscosity,
  RPIQuoteU: UcumCommon_RPIQuoteU,
  RubellaVirus: UcumCommon_RubellaVirus,
  RubellaVirus_2: UcumCommon_RubellaVirus_2,
  SignalToCutoffRatio: UcumCommon_SignalToCutoffRatio,
  SatIndex: UcumCommon_SatIndex,
  Saturation: UcumCommon_Saturation,
  Scoop: UcumCommon_Scoop,
  ScoreOf: UcumCommon_ScoreOf,
  Shift: UcumCommon_Shift,
  SpermatozoaPerMilliliter: UcumCommon_SpermatozoaPerMilliliter,
  SpermatozoaPerMilliLiter: UcumCommon_SpermatozoaPerMilliLiter,
  Spray: UcumCommon_Spray,
  StandardDeviation: UcumCommon_StandardDeviation,
  StandardIgAQuoteU: UcumCommon_StandardIgAQuoteU,
  StandardIgGQuoteU: UcumCommon_StandardIgGQuoteU,
  StandardIgMQuoteU: UcumCommon_StandardIgMQuoteU,
  StdDeviationQuoteU: UcumCommon_StdDeviationQuoteU,
  StandardDeviation_2: UcumCommon_StandardDeviation_2,
  StimulatingIndex: UcumCommon_StimulatingIndex,
  StreptozymeQuoteU: UcumCommon_StreptozymeQuoteU,
  Tablets: UcumCommon_Tablets,
  ThyroxinUptakeQuoteU: UcumCommon_ThyroxinUptakeQuoteU,
  TIBCQuoteU: UcumCommon_TIBCQuoteU,
  TimesWeek: UcumCommon_TimesWeek,
  TineQuoteU: UcumCommon_TineQuoteU,
  Titer: UcumCommon_Titer,
  ParticlesTotalCount: UcumCommon_ParticlesTotalCount,
  ToxoplasmaIndexValue: UcumCommon_ToxoplasmaIndexValue,
  ThyroidStimulatingImmunoglobulinIndex: UcumCommon_ThyroidStimulatingImmunoglobulinIndex,
  Vial: UcumCommon_Vial,
  VolumeVvolume: UcumCommon_VolumeVvolume,
  WhiteBloodCells: UcumCommon_WhiteBloodCells,
  WeeksDays: UcumCommon_WeeksDays,
  WhiteBloodCell: UcumCommon_WhiteBloodCell,
  VAL1Asterisk: UcumCommon_VAL1Asterisk,
  OnePerDay: UcumCommon_OnePerDay,
  OnePerMinute: UcumCommon_OnePerMinute,
  TheNumberTenForArbitraryPowers: UcumCommon_TheNumberTenForArbitraryPowers,
  TrillionPerLiter: UcumCommon_TrillionPerLiter,
  Thousand: UcumCommon_Thousand,
  ThousandRedBloodCells: UcumCommon_ThousandRedBloodCells,
  ThousandPerAsteriskUnit: UcumCommon_ThousandPerAsteriskUnit,
  ThousandPerLiter: UcumCommon_ThousandPerLiter,
  ThousandPerMilliLiter: UcumCommon_ThousandPerMilliLiter,
  ThousandsPerMicroLiter: UcumCommon_ThousandsPerMicroLiter,
  ThousandCopiesPerMilliliter: UcumCommon_ThousandCopiesPerMilliliter,
  ThousandCopiesPerMilliLiter: UcumCommon_ThousandCopiesPerMilliLiter,
  TheNumberTenForArbitraryPowersPower3PolarizationQuoteU: UcumCommon_TheNumberTenForArbitraryPowersPower3PolarizationQuoteU,
  ThousandRedBloodCells_2: UcumCommon_ThousandRedBloodCells_2,
  VAL10ThousandPerMicroliter: UcumCommon_VAL10ThousandPerMicroliter,
  OneHundredThousand: UcumCommon_OneHundredThousand,
  Million: UcumCommon_Million,
  MillionColonyFormingUnitPerLiter: UcumCommon_MillionColonyFormingUnitPerLiter,
  MillionInternationalUnit: UcumCommon_MillionInternationalUnit,
  MillionInternationalUnit_2: UcumCommon_MillionInternationalUnit_2,
  MillionEquivalentsPerMilliLiter: UcumCommon_MillionEquivalentsPerMilliLiter,
  TheNumberTenForArbitraryPowersPower6AsteriskUnit: UcumCommon_TheNumberTenForArbitraryPowersPower6AsteriskUnit,
  MillionPer24Hour: UcumCommon_MillionPer24Hour,
  MillionPerSpecimen: UcumCommon_MillionPerSpecimen,
  MillionPerKilogram: UcumCommon_MillionPerKilogram,
  MillionPerLiter: UcumCommon_MillionPerLiter,
  MillionPerMilliliter: UcumCommon_MillionPerMilliliter,
  TheNumberTenForArbitraryPowersPower6MillimeterPower3: UcumCommon_TheNumberTenForArbitraryPowersPower6MillimeterPower3,
  MillionPerMicroliter: UcumCommon_MillionPerMicroliter,
  TheNumberTenForArbitraryPowersPower6ImmunofluorescenceQuoteU: UcumCommon_TheNumberTenForArbitraryPowersPower6ImmunofluorescenceQuoteU,
  TenToEighth: UcumCommon_TenToEighth,
  BillionPerLiter: UcumCommon_BillionPerLiter,
  BillionPerMilliliter: UcumCommon_BillionPerMilliliter,
  BillionPerMicroliter: UcumCommon_BillionPerMicroliter,
  VAL10LiterPerMinutePerSquareMeter: UcumCommon_VAL10LiterPerMinutePerSquareMeter,
  VAL10LiterPerMinute: UcumCommon_VAL10LiterPerMinute,
  VAL10AsteriskMicroNewtonAsteriskSecondCentimeterAsteriskMeterPower2: UcumCommon_VAL10AsteriskMicroNewtonAsteriskSecondCentimeterAsteriskMeterPower2,
  VAL10MicronewtonSecondPerCentimeterToTheFifthPowerPerSquareMeter: UcumCommon_VAL10MicronewtonSecondPerCentimeterToTheFifthPowerPerSquareMeter,
  VAL10AsteriskMicroNewtonAsteriskSecondCentimeter: UcumCommon_VAL10AsteriskMicroNewtonAsteriskSecondCentimeter,
  VAL10AsteriskMicroNewtonAsteriskSecondCentimeterPower2: UcumCommon_VAL10AsteriskMicroNewtonAsteriskSecondCentimeterPower2,
  TheNumberTenForArbitraryPowers_2: UcumCommon_TheNumberTenForArbitraryPowers_2,
  VAL24Hour: UcumCommon_VAL24Hour,
  Years: AgeUnits_Years,
  Ampre: UcumCommon_Ampre,
  AmpreMeter: UcumCommon_AmpreMeter,
  MeanGregorianYear: UcumCommon_MeanGregorianYear,
  MeanJulianYear: UcumCommon_MeanJulianYear,
  TropicalYear: UcumCommon_TropicalYear,
  AttogramPerCell: UcumCommon_AttogramPerCell,
  Ngstrm: UcumCommon_Ngstrm,
  Are: UcumCommon_Are,
  StandardAtmosphere: UcumCommon_StandardAtmosphere,
  TechnicalAtmosphere: UcumCommon_TechnicalAtmosphere,
  AstronomicUnit: UcumCommon_AstronomicUnit,
  Barn: UcumCommon_Barn,
  Bel: UcumCommon_Bel,
  BelKilowatt: UcumCommon_BelKilowatt,
  BelMillivolt: UcumCommon_BelMillivolt,
  BelSoundPressure: UcumCommon_BelSoundPressure,
  BelMicrovolt: UcumCommon_BelMicrovolt,
  BelVolt: UcumCommon_BelVolt,
  BelWatt: UcumCommon_BelWatt,
  Bar: UcumCommon_Bar,
  Baud: UcumCommon_Baud,
  Biot: UcumCommon_Biot,
  Bit: UcumCommon_Bit,
  Bit_2: UcumCommon_Bit_2,
  Becquerel: UcumCommon_Becquerel,
  Byte: UcumCommon_Byte,
  Coulomb: UcumCommon_Coulomb,
  Calorie: UcumCommon_Calorie,
  CalorieAt15DegreesC: UcumCommon_CalorieAt15DegreesC,
  CalorieAt20DegreesC: UcumCommon_CalorieAt20DegreesC,
  InternationalTableCalorie: UcumCommon_InternationalTableCalorie,
  MeanCalorie: UcumCommon_MeanCalorie,
  ThermochemicalCalorie: UcumCommon_ThermochemicalCalorie,
  Candela: UcumCommon_Candela,
  NONE_4: UcumBodytemp_NONE_2,
  Centigram: UcumCommon_Centigram,
  CURIE: UcumCommon_CURIE,
  Circle: UcumCommon_Circle,
  Centiliter: UcumCommon_Centiliter,
  NONE_5: UcumBodylength_NONE_2,
  CentimeterOfWater: UcumCommon_CentimeterOfWater,
  CentimeterOfWaterColumnSecondAsteriskMeter: UcumCommon_CentimeterOfWaterColumnSecondAsteriskMeter,
  CentimeterOfWaterPerLiterPerSecond: UcumCommon_CentimeterOfWaterPerLiterPerSecond,
  CentimeterOfWaterPerSecondPerMeter: UcumCommon_CentimeterOfWaterPerSecondPerMeter,
  CentimeterOfMercury: UcumCommon_CentimeterOfMercury,
  SquareCentimeter: UcumCommon_SquareCentimeter,
  SquareCentimeterPerSecond: UcumCommon_SquareCentimeterPerSecond,
  CubicCentimeter: UcumCommon_CubicCentimeter,
  CentiPoise: UcumCommon_CentiPoise,
  CentiStokes: UcumCommon_CentiStokes,
  Days: AgeUnits_Days,
  DekaliterPerMinute: UcumCommon_DekaliterPerMinute,
  DekaliterPerMinutePerSquareMeter: UcumCommon_DekaliterPerMinutePerSquareMeter,
  Decibel: UcumCommon_Decibel,
  Degree: UcumCommon_Degree,
  DegreePerSecond: UcumCommon_DegreePerSecond,
  Decigram: UcumCommon_Decigram,
  Deciliter: UcumCommon_Deciliter,
  Decimeter: UcumCommon_Decimeter,
  SquareDecimeterPerSquareSecond: UcumCommon_SquareDecimeterPerSquareSecond,
  Dyne: UcumCommon_Dyne,
  DyneSecondPerCentimeterPerSquareMeter: UcumCommon_DyneSecondPerCentimeterPerSquareMeter,
  DyneSecondPerCentimeter: UcumCommon_DyneSecondPerCentimeter,
  Equivalents: UcumCommon_Equivalents,
  EquivalentsLiter: UcumCommon_EquivalentsLiter,
  EquivalentsMilliliter: UcumCommon_EquivalentsMilliliter,
  EquivalentsMillimole: UcumCommon_EquivalentsMillimole,
  EquivalentsMicromole: UcumCommon_EquivalentsMicromole,
  Erg: UcumCommon_Erg,
  Electronvolt: UcumCommon_Electronvolt,
  Farad: UcumCommon_Farad,
  Femtogram: UcumCommon_Femtogram,
  Femtoliter: UcumCommon_Femtoliter,
  FemtoliterNanoliter: UcumCommon_FemtoliterNanoliter,
  Femtometer: UcumCommon_Femtometer,
  Femtomole: UcumCommon_Femtomole,
  FemtomolePerGram: UcumCommon_FemtomolePerGram,
  FemtomolePerLiter: UcumCommon_FemtomolePerLiter,
  FemtomoleMilligram: UcumCommon_FemtomoleMilligram,
  FemtomolePerMilligramOfCytosolProtein: UcumCommon_FemtomolePerMilligramOfCytosolProtein,
  FemtomolePerMilligramOfProtein: UcumCommon_FemtomolePerMilligramOfProtein,
  FemtomoleMilliliter: UcumCommon_FemtomoleMilliliter,
  NONE_6: UcumBodyweight_NONE_2,
  Gauss: UcumCommon_Gauss,
  GramPercent: UcumCommon_GramPercent,
  GramAsteriskMeter: UcumCommon_GramAsteriskMeter,
  GramAsteriskMeterHbAsteriskMeterPower2: UcumCommon_GramAsteriskMeterHbAsteriskMeterPower2,
  GramMeterPerHeartBeat: UcumCommon_GramMeterPerHeartBeat,
  GramMeterPerHeartbeat: UcumCommon_GramMeterPerHeartbeat,
  GramAsteriskMeterHb: UcumCommon_GramAsteriskMeterHb,
  GramPer100Gram: UcumCommon_GramPer100Gram,
  GramPer12Hour: UcumCommon_GramPer12Hour,
  GramPer24Hour: UcumCommon_GramPer24Hour,
  GramPer3Days: UcumCommon_GramPer3Days,
  GramPer4Hour: UcumCommon_GramPer4Hour,
  GramPer48Hour: UcumCommon_GramPer48Hour,
  GramPer5Hour: UcumCommon_GramPer5Hour,
  GramPer6Hour: UcumCommon_GramPer6Hour,
  GramPer72Hour: UcumCommon_GramPer72Hour,
  Gram8AsteriskHour: UcumCommon_Gram8AsteriskHour,
  GramPer8HourShift: UcumCommon_GramPer8HourShift,
  Gram8AsteriskKilogramAsteriskHour: UcumCommon_Gram8AsteriskKilogramAsteriskHour,
  GramKilogramAsteriskHour: UcumCommon_GramKilogramAsteriskHour,
  GramKilogramAsteriskMinute: UcumCommon_GramKilogramAsteriskMinute,
  GramPerSpecimen: UcumCommon_GramPerSpecimen,
  GramPerTotalOutput: UcumCommon_GramPerTotalOutput,
  GramPerTotalWeight: UcumCommon_GramPerTotalWeight,
  GramTotalWeight: UcumCommon_GramTotalWeight,
  GramPerCubicCentimeter: UcumCommon_GramPerCubicCentimeter,
  GramPerDay: UcumCommon_GramPerDay,
  GramPerDeciliter: UcumCommon_GramPerDeciliter,
  GramPerGram: UcumCommon_GramPerGram,
  GramGramCre: UcumCommon_GramGramCre,
  GramGramCreat: UcumCommon_GramGramCreat,
  GramPerGramOfGlobulin: UcumCommon_GramPerGramOfGlobulin,
  GramPerGramOfTissue: UcumCommon_GramPerGramOfTissue,
  GramPerHour: UcumCommon_GramPerHour,
  GramPerHourPerSquareMeter: UcumCommon_GramPerHourPerSquareMeter,
  GramPerKilogram: UcumCommon_GramPerKilogram,
  GramPerKilogramPer8Hour: UcumCommon_GramPerKilogramPer8Hour,
  GramPerKilogramPer8HourShift: UcumCommon_GramPerKilogramPer8HourShift,
  GramPerKilogramPerDay: UcumCommon_GramPerKilogramPerDay,
  GramPerKilogramPerHour: UcumCommon_GramPerKilogramPerHour,
  GramPerKilogramPerMinute: UcumCommon_GramPerKilogramPerMinute,
  GramPerLiter: UcumCommon_GramPerLiter,
  GramsPerSquareMeter: UcumCommon_GramsPerSquareMeter,
  GramPerMilligram: UcumCommon_GramPerMilligram,
  GramPerMinute: UcumCommon_GramPerMinute,
  GramPerMilliliter: UcumCommon_GramPerMilliliter,
  GramPerMillimole: UcumCommon_GramPerMillimole,
  GramMillimoleCreat: UcumCommon_GramMillimoleCreat,
  GramPerMole: UcumCommon_GramPerMole,
  GramPerMoleOfCreatinine: UcumCommon_GramPerMoleOfCreatinine,
  GramOfCreatinine: UcumCommon_GramOfCreatinine,
  GramOfHemoglobin: UcumCommon_GramOfHemoglobin,
  GramOfTotalNitrogen: UcumCommon_GramOfTotalNitrogen,
  GramOfTotalProtein: UcumCommon_GramOfTotalProtein,
  GramOfWetTissue: UcumCommon_GramOfWetTissue,
  Gal: UcumCommon_Gal,
  Gilbert: UcumCommon_Gilbert,
  GigaBecquerel: UcumCommon_GigaBecquerel,
  GramForce: UcumCommon_GramForce,
  GonGrade: UcumCommon_GonGrade,
  Gray: UcumCommon_Gray,
  Hours: AgeUnits_Hours,
  Henry: UcumCommon_Henry,
  Hectoliter: UcumCommon_Hectoliter,
  Hertz: UcumCommon_Hertz,
  Joule: UcumCommon_Joule,
  JoulePerLiter: UcumCommon_JoulePerLiter,
  Kelvin: UcumCommon_Kelvin,
  KelvinWatt: UcumCommon_KelvinWatt,
  KiloInternationalUnitPerLiter: UcumCommon_KiloInternationalUnitPerLiter,
  KiloInternationalUnitsPerMilliLiter: UcumCommon_KiloInternationalUnitsPerMilliLiter,
  KiloInternationalUnitPerMilliliter: UcumCommon_KiloInternationalUnitPerMilliliter,
  Katal: UcumCommon_Katal,
  KatalKilogram: UcumCommon_KatalKilogram,
  KatalLiter: UcumCommon_KatalLiter,
  KiloBecquerel: UcumCommon_KiloBecquerel,
  Kilocalorie: UcumCommon_Kilocalorie,
  Kilocalorie8AsteriskHour: UcumCommon_Kilocalorie8AsteriskHour,
  KilocaloriePerOunceUSAndBritish: UcumCommon_KilocaloriePerOunceUSAndBritish,
  KilocaloriePerDay: UcumCommon_KilocaloriePerDay,
  KilocaloriePerHour: UcumCommon_KilocaloriePerHour,
  KilocaloriePerKilogramPer24Hour: UcumCommon_KilocaloriePerKilogramPer24Hour,
  NONE_7: UcumBodyweight_NONE_3,
  KilogramMeterPerSecond: UcumCommon_KilogramMeterPerSecond,
  KilogramPerSecondPerSquareMeter: UcumCommon_KilogramPerSecondPerSquareMeter,
  KilogramPerHour: UcumCommon_KilogramPerHour,
  KilogramPerLiter: UcumCommon_KilogramPerLiter,
  KilogramMeterPower2: UcumCommon_KilogramMeterPower2,
  KilogramMeterPower3: UcumCommon_KilogramMeterPower3,
  KilogramMinute: UcumCommon_KilogramMinute,
  KilogramPerMole: UcumCommon_KilogramPerMole,
  KilogramSecond: UcumCommon_KilogramSecond,
  KilogramOfWetTissue: UcumCommon_KilogramOfWetTissue,
  Kiloliter: UcumCommon_Kiloliter,
  Kilometers: DistanceUnits_Kilometers,
  KiloPascal: UcumCommon_KiloPascal,
  Kilosecond: UcumCommon_Kilosecond,
  KiloEnzymeUnit: UcumCommon_KiloEnzymeUnit,
  KiloenzymeUnitPerGram: UcumCommon_KiloenzymeUnitPerGram,
  KiloUnitHour: UcumCommon_KiloUnitHour,
  KiloenzymeUnitPerLiter: UcumCommon_KiloenzymeUnitPerLiter,
  KiloEnzymeUnitPerLiterClass: UcumCommon_KiloEnzymeUnitPerLiterClass,
  KiloEnzymeUnitPerMilliliter: UcumCommon_KiloEnzymeUnitPerMilliliter,
  Kayser: UcumCommon_Kayser,
  Liter: UcumCommon_Liter,
  LiterAsteriskSecondPower2Second: UcumCommon_LiterAsteriskSecondPower2Second,
  LiterPer24Hour: UcumCommon_LiterPer24Hour,
  LiterPer8Hour: UcumCommon_LiterPer8Hour,
  LiterPerMinutePerSquareMeter: UcumCommon_LiterPerMinutePerSquareMeter,
  LiterPerDay: UcumCommon_LiterPerDay,
  LiterPerHour: UcumCommon_LiterPerHour,
  LiterPerKilogram: UcumCommon_LiterPerKilogram,
  LiterPerLiter: UcumCommon_LiterPerLiter,
  LiterPerMinute: UcumCommon_LiterPerMinute,
  LiterSecond: UcumCommon_LiterSecond,
  LiterPerSecondPerSquareSecond: UcumCommon_LiterPerSecondPerSquareSecond,
  Lumen: UcumCommon_Lumen,
  LumenSquareMeter: UcumCommon_LumenSquareMeter,
  LumenMeterPower2: UcumCommon_LumenMeterPower2,
  Lambert: UcumCommon_Lambert,
  Lux: UcumCommon_Lux,
  Meters: DistanceUnits_Meters,
  MeterPerSecond: UcumCommon_MeterPerSecond,
  MeterPerSquareSecond: UcumCommon_MeterPerSquareSecond,
  MeterOfWaterColumn: UcumCommon_MeterOfWaterColumn,
  MeterOfMercuryColumn: UcumCommon_MeterOfMercuryColumn,
  MilliinternationalUnit: UcumCommon_MilliinternationalUnit,
  MilliInternationalUnitPerLiter: UcumCommon_MilliInternationalUnitPerLiter,
  MilliInternationalUnitPerMilliliter: UcumCommon_MilliInternationalUnitPerMilliliter,
  SquareMeter: UcumCommon_SquareMeter,
  SquareMeterPerSecond: UcumCommon_SquareMeterPerSecond,
  CubicMeterPerSecond: UcumCommon_CubicMeterPerSecond,
  MilliAmpre: UcumCommon_MilliAmpre,
  Millibar: UcumCommon_Millibar,
  MillibarSecondPerLiter: UcumCommon_MillibarSecondPerLiter,
  MillibarPerLiterPerSecond: UcumCommon_MillibarPerLiterPerSecond,
  MegaBecquerel: UcumCommon_MegaBecquerel,
  MilliCurie: UcumCommon_MilliCurie,
  Milliequivalent: UcumCommon_Milliequivalent,
  MilliequivalentPer12Hour: UcumCommon_MilliequivalentPer12Hour,
  MilliequivalentPer2Hour: UcumCommon_MilliequivalentPer2Hour,
  MilliequivalentPer24Hour: UcumCommon_MilliequivalentPer24Hour,
  MilliequivalentPer8Hour: UcumCommon_MilliequivalentPer8Hour,
  Milliequivalents8AsteriskHourAsteriskKilogram: UcumCommon_Milliequivalents8AsteriskHourAsteriskKilogram,
  MilliequivalentsKilogramAsteriskDay: UcumCommon_MilliequivalentsKilogramAsteriskDay,
  MilliequivalentPerSpecimen: UcumCommon_MilliequivalentPerSpecimen,
  MilliequivalentsSpecimen: UcumCommon_MilliequivalentsSpecimen,
  MilliequivalentPerTotalVolume: UcumCommon_MilliequivalentPerTotalVolume,
  MilliequivalentPerDay: UcumCommon_MilliequivalentPerDay,
  MilliequivalentPerDeciliter: UcumCommon_MilliequivalentPerDeciliter,
  MilliequivalentPerGram: UcumCommon_MilliequivalentPerGram,
  MilliequivalentsGramCre: UcumCommon_MilliequivalentsGramCre,
  MilliequivalentPerGramOfCreatinine: UcumCommon_MilliequivalentPerGramOfCreatinine,
  MilliequivalentPerHour: UcumCommon_MilliequivalentPerHour,
  MilliequivalentPerKilogram: UcumCommon_MilliequivalentPerKilogram,
  MilliequivalentPerKilogramPerHour: UcumCommon_MilliequivalentPerKilogramPerHour,
  MilliequivalentsKilogramMinute: UcumCommon_MilliequivalentsKilogramMinute,
  MilliequivalentPerLiter: UcumCommon_MilliequivalentPerLiter,
  MilliequivalentPerSquareMeter: UcumCommon_MilliequivalentPerSquareMeter,
  MilliequivalentPerMinute: UcumCommon_MilliequivalentPerMinute,
  MilliequivalentPerMilliliter: UcumCommon_MilliequivalentPerMilliliter,
  Milligram: UcumCommon_Milligram,
  MilligramPer10Hour: UcumCommon_MilligramPer10Hour,
  MilligramPer12Hour: UcumCommon_MilligramPer12Hour,
  MilligramPer18Hour: UcumCommon_MilligramPer18Hour,
  MilligramPer2Hour: UcumCommon_MilligramPer2Hour,
  MilligramPer24Hour: UcumCommon_MilligramPer24Hour,
  MilligramPer6Hour: UcumCommon_MilligramPer6Hour,
  MilligramPer72Hour: UcumCommon_MilligramPer72Hour,
  MilligramPer8Hour: UcumCommon_MilligramPer8Hour,
  Milligram8AsteriskHourAsteriskKilogram: UcumCommon_Milligram8AsteriskHourAsteriskKilogram,
  MilligramKilogramAsteriskHour: UcumCommon_MilligramKilogramAsteriskHour,
  MilligramPerCollection: UcumCommon_MilligramPerCollection,
  MilligramHgbGram: UcumCommon_MilligramHgbGram,
  MilligramPerSpecimen: UcumCommon_MilligramPerSpecimen,
  MilligramSpecimen: UcumCommon_MilligramSpecimen,
  MilligramPerTotalOutput: UcumCommon_MilligramPerTotalOutput,
  MilligramPerTotalVolume: UcumCommon_MilligramPerTotalVolume,
  MilliGramPerTotalVolume: UcumCommon_MilliGramPerTotalVolume,
  MilligramTotQuoteVolume: UcumCommon_MilligramTotQuoteVolume,
  MilligramVolume: UcumCommon_MilligramVolume,
  MilligramPerDay: UcumCommon_MilligramPerDay,
  MilligramDay173AsteriskTheNumberTenForArbitraryPowersPower2AsteriskMeterPower2: UcumCommon_MilligramDay173AsteriskTheNumberTenForArbitraryPowersPower2AsteriskMeterPower2,
  MilligramPerDayPer173SquareMeter: UcumCommon_MilligramPerDayPer173SquareMeter,
  MilligramPerDeciliter: UcumCommon_MilligramPerDeciliter,
  MilligramPerDeciliterOfRedBloodCells: UcumCommon_MilligramPerDeciliterOfRedBloodCells,
  MilligramPerGram: UcumCommon_MilligramPerGram,
  MilligramGramCre: UcumCommon_MilligramGramCre,
  MilligramPerGramOfCreatinine: UcumCommon_MilligramPerGramOfCreatinine,
  MilligramPerGramOfDryTissue: UcumCommon_MilligramPerGramOfDryTissue,
  MilligramPerGramOfFeces: UcumCommon_MilligramPerGramOfFeces,
  MilligramPerGramOfTissue: UcumCommon_MilligramPerGramOfTissue,
  MilligramPerGramOfWetTissue: UcumCommon_MilligramPerGramOfWetTissue,
  MilligramPerHour: UcumCommon_MilligramPerHour,
  MilligramPerKilogram: UcumCommon_MilligramPerKilogram,
  MilligramKilogram24AsteriskHour: UcumCommon_MilligramKilogram24AsteriskHour,
  MilligramPerKilogramPer8Hour: UcumCommon_MilligramPerKilogramPer8Hour,
  MilligramPerKilogramPerDay: UcumCommon_MilligramPerKilogramPerDay,
  MilligramPerKilogramPerHour: UcumCommon_MilligramPerKilogramPerHour,
  MilligramPerKilogramPerMinute: UcumCommon_MilligramPerKilogramPerMinute,
  MilligramPerLiter: UcumCommon_MilligramPerLiter,
  MilligramPerLiterOfRedBloodCells: UcumCommon_MilligramPerLiterOfRedBloodCells,
  MilligramPerSquareMeter: UcumCommon_MilligramPerSquareMeter,
  MilligramPerCubicMeter: UcumCommon_MilligramPerCubicMeter,
  MilligramPerMilligram: UcumCommon_MilligramPerMilligram,
  MilligramMilligramCre: UcumCommon_MilligramMilligramCre,
  MilligramsPerMilligramCreatinine: UcumCommon_MilligramsPerMilligramCreatinine,
  MilligramPerMilligramOfCreatinine: UcumCommon_MilligramPerMilligramOfCreatinine,
  MilligramPerMinute: UcumCommon_MilligramPerMinute,
  MilligramPerMilliliter: UcumCommon_MilligramPerMilliliter,
  MilligramPerMillimole: UcumCommon_MilligramPerMillimole,
  MilligramMillimoleCre: UcumCommon_MilligramMillimoleCre,
  MilligramPerMillimoleOfCreatinine: UcumCommon_MilligramPerMillimoleOfCreatinine,
  MilligramPerWeek: UcumCommon_MilligramPerWeek,
  MilligramOfCreatinine: UcumCommon_MilligramOfCreatinine,
  MilligramFibrinogenEquivalentUnitPerLiter: UcumCommon_MilligramFibrinogenEquivalentUnitPerLiter,
  MilligramPhenylketonesDeciliter: UcumCommon_MilligramPhenylketonesDeciliter,
  Mho: UcumCommon_Mho,
  Minutes: AgeUnits_Minutes,
  Milliliter: UcumCommon_Milliliter,
  MilliliterHQuoteBAsteriskMeterPower2: UcumCommon_MilliliterHQuoteBAsteriskMeterPower2,
  MilliliterPer10Hour: UcumCommon_MilliliterPer10Hour,
  MilliliterPer12Hour: UcumCommon_MilliliterPer12Hour,
  MilliliterPer2Hour: UcumCommon_MilliliterPer2Hour,
  MilliliterPer24Hour: UcumCommon_MilliliterPer24Hour,
  MilliliterPer4Hour: UcumCommon_MilliliterPer4Hour,
  MilliliterPer5Hour: UcumCommon_MilliliterPer5Hour,
  MilliliterPer6Hour: UcumCommon_MilliliterPer6Hour,
  MilliliterPer72Hour: UcumCommon_MilliliterPer72Hour,
  MilliliterPer8Hour: UcumCommon_MilliliterPer8Hour,
  MilliliterKilogramAsteriskMinute: UcumCommon_MilliliterKilogramAsteriskMinute,
  MilliliterPerSquareInchInternational: UcumCommon_MilliliterPerSquareInchInternational,
  MilliliterPerHeartBeat: UcumCommon_MilliliterPerHeartBeat,
  MilliliterPerHeartBeatPerSquareMeter: UcumCommon_MilliliterPerHeartBeatPerSquareMeter,
  MilliLitersPerHeartbeatSIVolumeUnits: UcumCommon_MilliLitersPerHeartbeatSIVolumeUnits,
  MilliliterCentimeterOfWaterColumn: UcumCommon_MilliliterCentimeterOfWaterColumn,
  MilliliterPerDay: UcumCommon_MilliliterPerDay,
  MilliliterPerDeciliter: UcumCommon_MilliliterPerDeciliter,
  MilliliterPerHour: UcumCommon_MilliliterPerHour,
  MilliliterPerKilogram: UcumCommon_MilliliterPerKilogram,
  MilliliterPerKilogramPer8Hour: UcumCommon_MilliliterPerKilogramPer8Hour,
  MilliliterPerKilogramPerDay: UcumCommon_MilliliterPerKilogramPerDay,
  MilliliterPerKilogramPerHour: UcumCommon_MilliliterPerKilogramPerHour,
  MilliliterPerKilogramPerMinute: UcumCommon_MilliliterPerKilogramPerMinute,
  MilliliterPerLiter: UcumCommon_MilliliterPerLiter,
  MilliliterPerSquareMeter: UcumCommon_MilliliterPerSquareMeter,
  MilliliterPerMillibar: UcumCommon_MilliliterPerMillibar,
  MilliliterPerMinute: UcumCommon_MilliliterPerMinute,
  MilliliterMinute173AsteriskTheNumberTenForArbitraryPowersPower2AsteriskMeterPower2: UcumCommon_MilliliterMinute173AsteriskTheNumberTenForArbitraryPowersPower2AsteriskMeterPower2,
  MilliliterPerMinutePer173SquareMeter: UcumCommon_MilliliterPerMinutePer173SquareMeter,
  MilliliterPerMinutePerSquareMeter: UcumCommon_MilliliterPerMinutePerSquareMeter,
  MilliliterPerMillimeter: UcumCommon_MilliliterPerMillimeter,
  MilliliterPerSecond: UcumCommon_MilliliterPerSecond,
  MilliliterOfFetalRedBloodCells: UcumCommon_MilliliterOfFetalRedBloodCells,
  Millimeters: DistanceUnits_Millimeters,
  MillimeterPerHour: UcumCommon_MillimeterPerHour,
  MillimeterPerMinute: UcumCommon_MillimeterPerMinute,
  MillimeterOfWater: UcumCommon_MillimeterOfWater,
  MillimeterOfMercury: UcumCommon_MillimeterOfMercury,
  SquareMillimeter: UcumCommon_SquareMillimeter,
  CubicMillimeter: UcumCommon_CubicMillimeter,
  Millimole: UcumCommon_Millimole,
  MillimolePer12Hour: UcumCommon_MillimolePer12Hour,
  MillimolePer18Hour: UcumCommon_MillimolePer18Hour,
  MillimolePer2Hour: UcumCommon_MillimolePer2Hour,
  MillimolePer24Hour: UcumCommon_MillimolePer24Hour,
  MillimolePer5Hour: UcumCommon_MillimolePer5Hour,
  MillimolePer6Hour: UcumCommon_MillimolePer6Hour,
  MillimolePer8Hour: UcumCommon_MillimolePer8Hour,
  Millimole8AsteriskHourAsteriskKilogram: UcumCommon_Millimole8AsteriskHourAsteriskKilogram,
  MillimolePerEjaculate: UcumCommon_MillimolePerEjaculate,
  MillimolePerSpecimen: UcumCommon_MillimolePerSpecimen,
  MillimolePerTotalVolume: UcumCommon_MillimolePerTotalVolume,
  MilliMolesPerTotalVolumeSubstanceUnits: UcumCommon_MilliMolesPerTotalVolumeSubstanceUnits,
  MillimoleTotQuoteVolume: UcumCommon_MillimoleTotQuoteVolume,
  MillimolePerDay: UcumCommon_MillimolePerDay,
  MillimolePerDeciliter: UcumCommon_MillimolePerDeciliter,
  MillimolePerGram: UcumCommon_MillimolePerGram,
  MillimolePerGramOfCreatinine: UcumCommon_MillimolePerGramOfCreatinine,
  MillimolePerGramOfHemoglobin: UcumCommon_MillimolePerGramOfHemoglobin,
  MillimolePerHour: UcumCommon_MillimolePerHour,
  MillimolePerHourPerMilligramOfHemoglobin: UcumCommon_MillimolePerHourPerMilligramOfHemoglobin,
  MillimolePerHourPerMilligramOfProtein: UcumCommon_MillimolePerHourPerMilligramOfProtein,
  MillimolePerKilogram: UcumCommon_MillimolePerKilogram,
  MillimolePerKilogramPer8Hour: UcumCommon_MillimolePerKilogramPer8Hour,
  MillimolePerKilogramPerDay: UcumCommon_MillimolePerKilogramPerDay,
  MillimolePerKilogramPerHour: UcumCommon_MillimolePerKilogramPerHour,
  MillimolePerKilogramPerMinute: UcumCommon_MillimolePerKilogramPerMinute,
  MillimolePerKilogramOfWater: UcumCommon_MillimolePerKilogramOfWater,
  MillimolePerLiter: UcumCommon_MillimolePerLiter,
  MillimolePerLiterPerSecond: UcumCommon_MillimolePerLiterPerSecond,
  MillimolePerLiterOfRedBloodCells: UcumCommon_MillimolePerLiterOfRedBloodCells,
  MillimoleMeter: UcumCommon_MillimoleMeter,
  MillimolePerSquareMeter: UcumCommon_MillimolePerSquareMeter,
  MillimolePerMinute: UcumCommon_MillimolePerMinute,
  MillimolePerMillimole: UcumCommon_MillimolePerMillimole,
  MillimolePerMillmoleOfCreatinine: UcumCommon_MillimolePerMillmoleOfCreatinine,
  MillimolePerMillimoleOfUrea: UcumCommon_MillimolePerMillimoleOfUrea,
  MillimolePerMole: UcumCommon_MillimolePerMole,
  MilliMolesPerMoleCreatinine: UcumCommon_MilliMolesPerMoleCreatinine,
  MillimolePerMoleOfCreatinine: UcumCommon_MillimolePerMoleOfCreatinine,
  MillimolePerSecondPerLiter: UcumCommon_MillimolePerSecondPerLiter,
  Months: AgeUnits_Months,
  MeanGregorianMonth: UcumCommon_MeanGregorianMonth,
  MeanJulianMonth: UcumCommon_MeanJulianMonth,
  SynodalMonth: UcumCommon_SynodalMonth,
  Mole: UcumCommon_Mole,
  MolePerDay: UcumCommon_MolePerDay,
  MolePerKilogram: UcumCommon_MolePerKilogram,
  MolePerKilogramPerSecond: UcumCommon_MolePerKilogramPerSecond,
  MolePerLiter: UcumCommon_MolePerLiter,
  MolePerCubicMeter: UcumCommon_MolePerCubicMeter,
  MolePerMilliliter: UcumCommon_MolePerMilliliter,
  MolePerMole: UcumCommon_MolePerMole,
  MoleMoleCreat: UcumCommon_MoleMoleCreat,
  MolePerSecond: UcumCommon_MolePerSecond,
  Milliosmole: UcumCommon_Milliosmole,
  MilliosmolePerKilogram: UcumCommon_MilliosmolePerKilogram,
  MilliosmolePerLiter: UcumCommon_MilliosmolePerLiter,
  Millipascal: UcumCommon_Millipascal,
  MillipascalSecond: UcumCommon_MillipascalSecond,
  Milliseconds: DurationUnits_Milliseconds,
  Megasecond: UcumCommon_Megasecond,
  MillienzymeUnit: UcumCommon_MillienzymeUnit,
  MillienzymeUnitPerGram: UcumCommon_MillienzymeUnitPerGram,
  MilliEnzymeUnitPerGramOfHemoglobin: UcumCommon_MilliEnzymeUnitPerGramOfHemoglobin,
  MilliUnitGramHgb: UcumCommon_MilliUnitGramHgb,
  MilliEnzymeUnitPerGramOfProtein: UcumCommon_MilliEnzymeUnitPerGramOfProtein,
  MillienzymeUnitPerLiter: UcumCommon_MillienzymeUnitPerLiter,
  MilliUnitMilligram: UcumCommon_MilliUnitMilligram,
  MilliUnitMilligramCre: UcumCommon_MilliUnitMilligramCre,
  MilliEnzymeUnitPerMilligramOfCreatinine: UcumCommon_MilliEnzymeUnitPerMilligramOfCreatinine,
  MilliUnitMinute: UcumCommon_MilliUnitMinute,
  MillienzymeUnitPerMilliliter: UcumCommon_MillienzymeUnitPerMilliliter,
  MillienzymeUnitPerMilliliterPerMinute: UcumCommon_MillienzymeUnitPerMilliliterPerMinute,
  MilliEnzymeUnitPerMillimoleOfCreatinine: UcumCommon_MilliEnzymeUnitPerMillimoleOfCreatinine,
  MillienzymeUnitPerMillimoleOfCreatinine: UcumCommon_MillienzymeUnitPerMillimoleOfCreatinine,
  MillienzymeUnitPerMillimoleOfRedBloodCells: UcumCommon_MillienzymeUnitPerMillimoleOfRedBloodCells,
  MilliVolt: UcumCommon_MilliVolt,
  Maxwell: UcumCommon_Maxwell,
  Newton: UcumCommon_Newton,
  NewtonCentimeter: UcumCommon_NewtonCentimeter,
  NewtonSecond: UcumCommon_NewtonSecond,
  NanoCurie: UcumCommon_NanoCurie,
  Nanogram: UcumCommon_Nanogram,
  NanogramPer24Hour: UcumCommon_NanogramPer24Hour,
  NanogramPer8Hour: UcumCommon_NanogramPer8Hour,
  Nanogram8AsteriskHourAsteriskKilogram: UcumCommon_Nanogram8AsteriskHourAsteriskKilogram,
  NanogramKilogramAsteriskDay: UcumCommon_NanogramKilogramAsteriskDay,
  NanogramKilogramAsteriskHour: UcumCommon_NanogramKilogramAsteriskHour,
  NanogramKilogramAsteriskMinute: UcumCommon_NanogramKilogramAsteriskMinute,
  NanogramPerMillion: UcumCommon_NanogramPerMillion,
  NanogramPerMillionRedBloodCells: UcumCommon_NanogramPerMillionRedBloodCells,
  NanogramPerDay: UcumCommon_NanogramPerDay,
  NanogramPerDeciliter: UcumCommon_NanogramPerDeciliter,
  NanogramDeciliterHour: UcumCommon_NanogramDeciliterHour,
  NanogramPerGram: UcumCommon_NanogramPerGram,
  NanogramGramCre: UcumCommon_NanogramGramCre,
  NanogramPerGramOfCreatinine: UcumCommon_NanogramPerGramOfCreatinine,
  NanogramPerHour: UcumCommon_NanogramPerHour,
  NanogramPerKilogram: UcumCommon_NanogramPerKilogram,
  NanogramPerKilogramPer8Hour: UcumCommon_NanogramPerKilogramPer8Hour,
  NanogramPerKilogramPerHour: UcumCommon_NanogramPerKilogramPerHour,
  NanogramPerKilogramPerMinute: UcumCommon_NanogramPerKilogramPerMinute,
  NanogramPerLiter: UcumCommon_NanogramPerLiter,
  NanogramPerSquareMeter: UcumCommon_NanogramPerSquareMeter,
  NanogramPerMilligram: UcumCommon_NanogramPerMilligram,
  NanogramPerMilligramPerHour: UcumCommon_NanogramPerMilligramPerHour,
  NanogramPerMilligramOfCreatinine: UcumCommon_NanogramPerMilligramOfCreatinine,
  NanogramPerMilligramOfProtein: UcumCommon_NanogramPerMilligramOfProtein,
  NanogramMilligramProtein: UcumCommon_NanogramMilligramProtein,
  NanogramPerMinute: UcumCommon_NanogramPerMinute,
  NanogramPerMillliiter: UcumCommon_NanogramPerMillliiter,
  NanogramPerMilliliterPerHour: UcumCommon_NanogramPerMilliliterPerHour,
  NanogramMilliliterRbc: UcumCommon_NanogramMilliliterRbc,
  NanogramPerMilliliterOfRedBloodCells: UcumCommon_NanogramPerMilliliterOfRedBloodCells,
  NanogramPerSecond: UcumCommon_NanogramPerSecond,
  NanogramPerEnzymeUnit: UcumCommon_NanogramPerEnzymeUnit,
  NanogramFibrinogenEquivalentUnitPerMilliliter: UcumCommon_NanogramFibrinogenEquivalentUnitPerMilliliter,
  Nanokatal: UcumCommon_Nanokatal,
  Nanoliter: UcumCommon_Nanoliter,
  Nanometers: DistanceUnits_Nanometers,
  NanometerPerSecondPerLiter: UcumCommon_NanometerPerSecondPerLiter,
  Nanomole: UcumCommon_Nanomole,
  NanomolePer24Hour: UcumCommon_NanomolePer24Hour,
  NanomolePerDay: UcumCommon_NanomolePerDay,
  NanomolePerDeciliter: UcumCommon_NanomolePerDeciliter,
  NanomolePerDeciliterOfGlomerularFiltrate: UcumCommon_NanomolePerDeciliterOfGlomerularFiltrate,
  NanomolePerGram: UcumCommon_NanomolePerGram,
  NanomoleGramCre: UcumCommon_NanomoleGramCre,
  NanomolePerGramOfCreatinine: UcumCommon_NanomolePerGramOfCreatinine,
  NanomolePerGramOfDryWeight: UcumCommon_NanomolePerGramOfDryWeight,
  NanomolePerHourPerLiter: UcumCommon_NanomolePerHourPerLiter,
  NanomolePerHourPerMilligramOfProtein: UcumCommon_NanomolePerHourPerMilligramOfProtein,
  NanomolePerHourPerMilliliter: UcumCommon_NanomolePerHourPerMilliliter,
  NanomolePerLiter: UcumCommon_NanomolePerLiter,
  NanomolePerLiterPerMillimoleOfCreatinine: UcumCommon_NanomolePerLiterPerMillimoleOfCreatinine,
  NanomolePerLiterPerSecond: UcumCommon_NanomolePerLiterPerSecond,
  NanomolePerLiterOfRedBloodCells: UcumCommon_NanomolePerLiterOfRedBloodCells,
  NanomolePerMeterPerMilligramOfProtein: UcumCommon_NanomolePerMeterPerMilligramOfProtein,
  NanomolePerMilligram: UcumCommon_NanomolePerMilligram,
  NanomolePerMilligramPerHour: UcumCommon_NanomolePerMilligramPerHour,
  NanomolePerMilligramOfCreatinine: UcumCommon_NanomolePerMilligramOfCreatinine,
  NanomolePerMilligramOfProtein: UcumCommon_NanomolePerMilligramOfProtein,
  NanomolePerMilligramOfProteinPerHour: UcumCommon_NanomolePerMilligramOfProteinPerHour,
  NanomolePerMinute: UcumCommon_NanomolePerMinute,
  NanomolePerMinutePerMillionCells: UcumCommon_NanomolePerMinutePerMillionCells,
  NanomolePerMinutePerMilligramOfHemoglobin: UcumCommon_NanomolePerMinutePerMilligramOfHemoglobin,
  NanomolePerMinutePerMilligramOfHemoglobin_2: UcumCommon_NanomolePerMinutePerMilligramOfHemoglobin_2,
  NanomolePerMinutePerMilligramOfProtein: UcumCommon_NanomolePerMinutePerMilligramOfProtein,
  NanomolePerMinutePerMilliliter: UcumCommon_NanomolePerMinutePerMilliliter,
  NanomolePerMilliliter: UcumCommon_NanomolePerMilliliter,
  NanomolePerMilliliterPerHour: UcumCommon_NanomolePerMilliliterPerHour,
  NanomolePerMilliliterPerMinute: UcumCommon_NanomolePerMilliliterPerMinute,
  NanomolePerMillimole: UcumCommon_NanomolePerMillimole,
  NanomoleMillimoleCre: UcumCommon_NanomoleMillimoleCre,
  NanomolePerMillimoleOfCreatinine: UcumCommon_NanomolePerMillimoleOfCreatinine,
  NanomolePerMole: UcumCommon_NanomolePerMole,
  NanomolePerNanomole: UcumCommon_NanomolePerNanomole,
  NanomolePerSecond: UcumCommon_NanomolePerSecond,
  NanomolePerSecondPerLiter: UcumCommon_NanomolePerSecondPerLiter,
  NanomolePerMicromoleOfCreatinine: UcumCommon_NanomolePerMicromoleOfCreatinine,
  NanomoleOf12CystinePerMilligramOfProtein: UcumCommon_NanomoleOf12CystinePerMilligramOfProtein,
  NanomoleOfATP: UcumCommon_NanomoleOfATP,
  NanomoleBoneCollagenEquivalent: UcumCommon_NanomoleBoneCollagenEquivalent,
  NanomoleBoneCollagenEquivalentPerLiter: UcumCommon_NanomoleBoneCollagenEquivalentPerLiter,
  NanomoleBoneCollagenEquivalentPerMillimoleOfCreatinine: UcumCommon_NanomoleBoneCollagenEquivalentPerMillimoleOfCreatinine,
  Neper: UcumCommon_Neper,
  Nanosecond: UcumCommon_Nanosecond,
  NanoenzymeUnitPerRedBloodCell: UcumCommon_NanoenzymeUnitPerRedBloodCell,
  NanoenzymeUnitPerMilliliter: UcumCommon_NanoenzymeUnitPerMilliliter,
  Oersted: UcumCommon_Oersted,
  Ohm: UcumCommon_Ohm,
  OhmMeter: UcumCommon_OhmMeter,
  Osmole: UcumCommon_Osmole,
  OsmolePerKilogram: UcumCommon_OsmolePerKilogram,
  OsmolePerLiter: UcumCommon_OsmolePerLiter,
  Poise: UcumCommon_Poise,
  Picoampere: UcumCommon_Picoampere,
  Pascal: UcumCommon_Pascal,
  Parsec: UcumCommon_Parsec,
  Picogram: UcumCommon_Picogram,
  PicogramPerCell: UcumCommon_PicogramPerCell,
  PicogramPerRedBloodCell: UcumCommon_PicogramPerRedBloodCell,
  PicogramPerDeciliter: UcumCommon_PicogramPerDeciliter,
  PicogramPerLiter: UcumCommon_PicogramPerLiter,
  PicogramPerMilligram: UcumCommon_PicogramPerMilligram,
  PicogramPerMilligramOfCreatinine: UcumCommon_PicogramPerMilligramOfCreatinine,
  PicogramPerMilliliter: UcumCommon_PicogramPerMilliliter,
  PicogramPerMillimeter: UcumCommon_PicogramPerMillimeter,
  Phot: UcumCommon_Phot,
  Picokatal: UcumCommon_Picokatal,
  Picoliter: UcumCommon_Picoliter,
  Picometer: UcumCommon_Picometer,
  Picomole: UcumCommon_Picomole,
  PicomolePer24Hour: UcumCommon_PicomolePer24Hour,
  PicomolePerRedBloodCell: UcumCommon_PicomolePerRedBloodCell,
  PicomolePerDay: UcumCommon_PicomolePerDay,
  PicomolePerDeciliter: UcumCommon_PicomolePerDeciliter,
  PicomolePerGram: UcumCommon_PicomolePerGram,
  PicomolePerHourPerMilligramOfProtein: UcumCommon_PicomolePerHourPerMilligramOfProtein,
  PicomolePerHourPerMilliliter: UcumCommon_PicomolePerHourPerMilliliter,
  PicomolePerLiter: UcumCommon_PicomolePerLiter,
  PicomolePerMilligramOfProtein: UcumCommon_PicomolePerMilligramOfProtein,
  PicomolePerMinute: UcumCommon_PicomolePerMinute,
  PicomolePerMinutePerMilligramOfProtein: UcumCommon_PicomolePerMinutePerMilligramOfProtein,
  PicomolePerMilliliter: UcumCommon_PicomolePerMilliliter,
  PicomolePerMillimole: UcumCommon_PicomolePerMillimole,
  PicomolePerMillimoleOfCreatinine: UcumCommon_PicomolePerMillimoleOfCreatinine,
  PicomolePerMole: UcumCommon_PicomolePerMole,
  PicomolePerMicromole: UcumCommon_PicomolePerMicromole,
  PicomolePerMicromoleOfCreatinine: UcumCommon_PicomolePerMicromoleOfCreatinine,
  Picosecond: UcumCommon_Picosecond,
  Picotesla: UcumCommon_Picotesla,
  Roentgen: UcumCommon_Roentgen,
  Radian: UcumCommon_Radian,
  RadiationAbsorbedDose: UcumCommon_RadiationAbsorbedDose,
  RadiationEquivalentMan: UcumCommon_RadiationEquivalentMan,
  Seconds: DurationUnits_Seconds,
  Siemens: UcumCommon_Siemens,
  SecondPerControl: UcumCommon_SecondPerControl,
  Stilb: UcumCommon_Stilb,
  Spere: UcumCommon_Spere,
  Steradian: UcumCommon_Steradian,
  Stere: UcumCommon_Stere,
  Stokes: UcumCommon_Stokes,
  Sievert: UcumCommon_Sievert,
  Tonne: UcumCommon_Tonne,
  Tesla: UcumCommon_Tesla,
  UnifiedAtomicMassUnit: UcumCommon_UnifiedAtomicMassUnit,
  EnzymeUnit: UcumCommon_EnzymeUnit,
  EnzymeUnitPer1Hour: UcumCommon_EnzymeUnitPer1Hour,
  EnzymeUnitPer10GramOfFeces: UcumCommon_EnzymeUnitPer10GramOfFeces,
  EnzymeUnitPer12Hour: UcumCommon_EnzymeUnitPer12Hour,
  EnzymeUnitPer18Hour: UcumCommon_EnzymeUnitPer18Hour,
  EnzymeUnitPer2Hour: UcumCommon_EnzymeUnitPer2Hour,
  EnzymeUnitPer24Hour: UcumCommon_EnzymeUnitPer24Hour,
  EnzymeUnitPer10: UcumCommon_EnzymeUnitPer10,
  EnzymeUnitPer10Billion: UcumCommon_EnzymeUnitPer10Billion,
  EnzymeUnitPer10BillionCells: UcumCommon_EnzymeUnitPer10BillionCells,
  EnzymeUnitPerTrillion: UcumCommon_EnzymeUnitPerTrillion,
  EnzymeUnitPerTrillionRedBloodCells: UcumCommon_EnzymeUnitPerTrillionRedBloodCells,
  EnzymeUnitPerMillion: UcumCommon_EnzymeUnitPerMillion,
  EnzymeUnitPerBillion: UcumCommon_EnzymeUnitPerBillion,
  EnzymeUnitPerDay: UcumCommon_EnzymeUnitPerDay,
  EnzymeUnitPerDeciliter: UcumCommon_EnzymeUnitPerDeciliter,
  EnzymeUnitPerGram: UcumCommon_EnzymeUnitPerGram,
  UnitGramCre: UcumCommon_UnitGramCre,
  EnzymeUnitPerGramOfCreatinine: UcumCommon_EnzymeUnitPerGramOfCreatinine,
  EnzymeUnitPerGramOfHemoglobin: UcumCommon_EnzymeUnitPerGramOfHemoglobin,
  EnzymeUnitPerGramOfHemoglobin_2: UcumCommon_EnzymeUnitPerGramOfHemoglobin_2,
  UnitsPerGramHemoglobin: UcumCommon_UnitsPerGramHemoglobin,
  EnzymeUnitPerGramOfProtein: UcumCommon_EnzymeUnitPerGramOfProtein,
  EnzymeUnitPerHour: UcumCommon_EnzymeUnitPerHour,
  UnitKilogramHour: UcumCommon_UnitKilogramHour,
  EnzymeUnitPerKilogramOfHemoglobin: UcumCommon_EnzymeUnitPerKilogramOfHemoglobin,
  EnzymeUnitPerKilogramOfHemoglobin_2: UcumCommon_EnzymeUnitPerKilogramOfHemoglobin_2,
  EnzymeUnitPerLiter: UcumCommon_EnzymeUnitPerLiter,
  EnzymeUnitPerMinute: UcumCommon_EnzymeUnitPerMinute,
  EnzymeUnitPerMilliliter: UcumCommon_EnzymeUnitPerMilliliter,
  UnitMilliliterRBC: UcumCommon_UnitMilliliterRBC,
  EnzymeUnitPerMilliliterOfRedBloodCells: UcumCommon_EnzymeUnitPerMilliliterOfRedBloodCells,
  EnzymeUnitPerMillimoleOfCreatinine: UcumCommon_EnzymeUnitPerMillimoleOfCreatinine,
  EnzymeUnitPerMole: UcumCommon_EnzymeUnitPerMole,
  EnzymeUnitPerSecond: UcumCommon_EnzymeUnitPerSecond,
  EnzymeUnitPerMicromole: UcumCommon_EnzymeUnitPerMicromole,
  MicroInternationalUnit: UcumCommon_MicroInternationalUnit,
  MicrointernationalUnitPerLiter: UcumCommon_MicrointernationalUnitPerLiter,
  MicroInternationalUnitPerMilliliter: UcumCommon_MicroInternationalUnitPerMilliliter,
  EnzymeUnitPerLiterAt25DegCelsius: UcumCommon_EnzymeUnitPerLiterAt25DegCelsius,
  EnzymeUnitPerLiterAt37DegCelsius: UcumCommon_EnzymeUnitPerLiterAt37DegCelsius,
  GUnit: UcumCommon_GUnit,
  MICROCURIE: UcumCommon_MICROCURIE,
  Microequivalents: UcumCommon_Microequivalents,
  MicroequivalentPerLiter: UcumCommon_MicroequivalentPerLiter,
  MicroequivalentPerMilliliter: UcumCommon_MicroequivalentPerMilliliter,
  Microgram: UcumCommon_Microgram,
  MicrogramPer100Gram: UcumCommon_MicrogramPer100Gram,
  MicrogramPer24Hour: UcumCommon_MicrogramPer24Hour,
  MicrogramPer8Hour: UcumCommon_MicrogramPer8Hour,
  MicrogramKilogramAsteriskDay: UcumCommon_MicrogramKilogramAsteriskDay,
  MicrogramKilogramAsteriskHour: UcumCommon_MicrogramKilogramAsteriskHour,
  MicrogramPerSquareFootInternational: UcumCommon_MicrogramPerSquareFootInternational,
  MicrogramPerSpecimen: UcumCommon_MicrogramPerSpecimen,
  MicrogramSpecimen: UcumCommon_MicrogramSpecimen,
  MicroGramsPerTotalVolume: UcumCommon_MicroGramsPerTotalVolume,
  MicrogramPerDay: UcumCommon_MicrogramPerDay,
  MicrogramPerDeciliter: UcumCommon_MicrogramPerDeciliter,
  MicrogramDeciliterRbc: UcumCommon_MicrogramDeciliterRbc,
  MicrogramPerDeciliterOfRedBloodCells: UcumCommon_MicrogramPerDeciliterOfRedBloodCells,
  MicrogramPerGram: UcumCommon_MicrogramPerGram,
  MicrogramGramCre: UcumCommon_MicrogramGramCre,
  MicrogramPerGramOfCreatinine: UcumCommon_MicrogramPerGramOfCreatinine,
  MicrogramPerGramOfDryTissue: UcumCommon_MicrogramPerGramOfDryTissue,
  MicrogramPerGramOfDryWeight: UcumCommon_MicrogramPerGramOfDryWeight,
  MicrogramGramDryWeight: UcumCommon_MicrogramGramDryWeight,
  MicrogramPerGramOfFeces: UcumCommon_MicrogramPerGramOfFeces,
  MicrogramPerGramOfHair: UcumCommon_MicrogramPerGramOfHair,
  MicrogramPerGramOfHemoglobin: UcumCommon_MicrogramPerGramOfHemoglobin,
  MicrogramGramHgb: UcumCommon_MicrogramGramHgb,
  MicrogramPerGramOfTissue: UcumCommon_MicrogramPerGramOfTissue,
  MicrogramGramTissue: UcumCommon_MicrogramGramTissue,
  MicrogramPerHour: UcumCommon_MicrogramPerHour,
  MicrogramPerKilogram: UcumCommon_MicrogramPerKilogram,
  MicrogramPerKilogramPer8Hour: UcumCommon_MicrogramPerKilogramPer8Hour,
  MicrogramPerKilogramPerDay: UcumCommon_MicrogramPerKilogramPerDay,
  MicrogramPerKilogramPerHour: UcumCommon_MicrogramPerKilogramPerHour,
  MicrogramPerKilogramPerMinute: UcumCommon_MicrogramPerKilogramPerMinute,
  MicrogramPerLiter: UcumCommon_MicrogramPerLiter,
  MicrogramPerLiterPer24Hour: UcumCommon_MicrogramPerLiterPer24Hour,
  MicrogramLiterDDU: UcumCommon_MicrogramLiterDDU,
  MicrogramPerLiterOfRedBloodCells: UcumCommon_MicrogramPerLiterOfRedBloodCells,
  MicrogramPerSquareMeter: UcumCommon_MicrogramPerSquareMeter,
  MicrogramPerCubicMeter: UcumCommon_MicrogramPerCubicMeter,
  MicrogramPerMilligram: UcumCommon_MicrogramPerMilligram,
  MicrogramMilligramCre: UcumCommon_MicrogramMilligramCre,
  MicrogramPerMilligramOfCreatinine: UcumCommon_MicrogramPerMilligramOfCreatinine,
  MicrogramPerMinute: UcumCommon_MicrogramPerMinute,
  MicrogramPerMilliliter: UcumCommon_MicrogramPerMilliliter,
  MicrogramPerMilliliterClass: UcumCommon_MicrogramPerMilliliterClass,
  MicrogramPerMilliliterEquivalent: UcumCommon_MicrogramPerMilliliterEquivalent,
  MicrogramMilliliterFEU: UcumCommon_MicrogramMilliliterFEU,
  MicrogramPerMillimole: UcumCommon_MicrogramPerMillimole,
  MicrogramPerMillimoleOfCreatinine: UcumCommon_MicrogramPerMillimoleOfCreatinine,
  MicrogramPerNanogram: UcumCommon_MicrogramPerNanogram,
  MicrogramFibrinogenEquivalentUnitPerMilliliter: UcumCommon_MicrogramFibrinogenEquivalentUnitPerMilliliter,
  MicrogramT4Deciliter: UcumCommon_MicrogramT4Deciliter,
  Microkatal: UcumCommon_Microkatal,
  Microliter: UcumCommon_Microliter,
  MicroliterPer2Hour: UcumCommon_MicroliterPer2Hour,
  MicroliterPerHour: UcumCommon_MicroliterPerHour,
  Micrometers: DistanceUnits_Micrometers,
  MicrometerPerSecond: UcumCommon_MicrometerPerSecond,
  Micromole: UcumCommon_Micromole,
  MicromolePer2Hour: UcumCommon_MicromolePer2Hour,
  MicromolePer24Hour: UcumCommon_MicromolePer24Hour,
  MicromolePer8Hour: UcumCommon_MicromolePer8Hour,
  MicromolePerDay: UcumCommon_MicromolePerDay,
  MicromolePerDeciliter: UcumCommon_MicromolePerDeciliter,
  MicromolePerDeciliterOfGlomerularFiltrate: UcumCommon_MicromolePerDeciliterOfGlomerularFiltrate,
  MicromolePerGram: UcumCommon_MicromolePerGram,
  MicromoleGramCre: UcumCommon_MicromoleGramCre,
  MicromolePerGramOfCreatinine: UcumCommon_MicromolePerGramOfCreatinine,
  MicromolePerGramOfHemoglobin: UcumCommon_MicromolePerGramOfHemoglobin,
  MicromolePerGramOfHemoglobin_2: UcumCommon_MicromolePerGramOfHemoglobin_2,
  MicromoleGramHgb: UcumCommon_MicromoleGramHgb,
  MicromolePerHour: UcumCommon_MicromolePerHour,
  MicromoleHourGram: UcumCommon_MicromoleHourGram,
  MicromolePerHourPerLiter: UcumCommon_MicromolePerHourPerLiter,
  MicromolePerHourPerMilligramOfProtein: UcumCommon_MicromolePerHourPerMilligramOfProtein,
  MicromolePerKilogram: UcumCommon_MicromolePerKilogram,
  MicromolePerKilogramOfFeces: UcumCommon_MicromolePerKilogramOfFeces,
  MicromolePerLiter: UcumCommon_MicromolePerLiter,
  MicromolePerLiterPerHour: UcumCommon_MicromolePerLiterPerHour,
  MicromolePerLiterOfRedBloodCells: UcumCommon_MicromolePerLiterOfRedBloodCells,
  MicromolePerLiterOfRedBloodCells_2: UcumCommon_MicromolePerLiterOfRedBloodCells_2,
  MicromoleMeter: UcumCommon_MicromoleMeter,
  MicromolePerMilligram: UcumCommon_MicromolePerMilligram,
  MicromoleMilligramCre: UcumCommon_MicromoleMilligramCre,
  MicromolePerMilligramOfCreatinine: UcumCommon_MicromolePerMilligramOfCreatinine,
  MicromolePerMinute: UcumCommon_MicromolePerMinute,
  MicromolePerMinutePerGram: UcumCommon_MicromolePerMinutePerGram,
  MicromolePerMinutePerGramOfMucosa: UcumCommon_MicromolePerMinutePerGramOfMucosa,
  MicromoleMinuteGramProt: UcumCommon_MicromoleMinuteGramProt,
  MicromolePerMinutePerGramOfProtein: UcumCommon_MicromolePerMinutePerGramOfProtein,
  MicromolePerMinutePerLiter: UcumCommon_MicromolePerMinutePerLiter,
  MicromolePerMilliliter: UcumCommon_MicromolePerMilliliter,
  MicromolePerMilliliterPerMinute: UcumCommon_MicromolePerMilliliterPerMinute,
  MicromolePerMillimole: UcumCommon_MicromolePerMillimole,
  MicromolePerMillimoleOfCreatinine: UcumCommon_MicromolePerMillimoleOfCreatinine,
  MicromolePerMole: UcumCommon_MicromolePerMole,
  MicromoleMoleCre: UcumCommon_MicromoleMoleCre,
  MicromolePerMoleOfCreatinine: UcumCommon_MicromolePerMoleOfCreatinine,
  MicromolePerMoleOfHemoglobin: UcumCommon_MicromolePerMoleOfHemoglobin,
  MicromolePerMicromole: UcumCommon_MicromolePerMicromole,
  MicromolePerMicromoleOfCreatinine: UcumCommon_MicromolePerMicromoleOfCreatinine,
  MicromoleBoneCollagenEquivalentPerMole: UcumCommon_MicromoleBoneCollagenEquivalentPerMole,
  MicroOhm: UcumCommon_MicroOhm,
  Microsecond: UcumCommon_Microsecond,
  MicroUnit: UcumCommon_MicroUnit,
  MicroEnzymeUnitPerGram: UcumCommon_MicroEnzymeUnitPerGram,
  MicroEnzymeUnitPerLiter: UcumCommon_MicroEnzymeUnitPerLiter,
  MicroEnzymeUnitPerMilliliter: UcumCommon_MicroEnzymeUnitPerMilliliter,
  Microvolt: UcumCommon_Microvolt,
  Volt: UcumCommon_Volt,
  Watt: UcumCommon_Watt,
  Weber: UcumCommon_Weber,
  Weeks: AgeUnits_Weeks,
};
/**
 * Common UCUM units for recording Vital Signs
 */
export const UcumVitalsCommon = {
  Percent: UcumCommon_Percent,
  PerMinute: UcumCommon_PerMinute,
  NONE: UcumBodytemp_NONE,
  NONE_2: UcumBodylength_NONE,
  NONE_3: UcumBodyweight_NONE,
  NONE_4: UcumBodytemp_NONE_2,
  NONE_5: UcumBodylength_NONE_2,
  NONE_6: UcumBodyweight_NONE_2,
  NONE_7: UcumBodyweight_NONE_3,
  KilogramMeterPower2: UcumCommon_KilogramMeterPower2,
  SquareMeter: UcumCommon_SquareMeter,
  MillimeterOfMercury: UcumCommon_MillimeterOfMercury,
};
const udi_GUDIDFDA: Coding = {
  code: 'gudid',
  display: 'GUDID (FDA)',
  system: 'http://hl7.org/fhir/ex-udi',
};
/**
 * This value set includes sample UDI codes.
 */
export const Udi = {
  /**
   * GUDID (FDA) US Repository
   */
  GUDIDFDA: udi_GUDIDFDA,
};
const udi_entry_type_Barcode: Coding = {
  code: 'barcode',
  display: 'Barcode',
  system: 'http://hl7.org/fhir/udi-entry-type',
};
const udi_entry_type_Card: Coding = {
  code: 'card',
  display: 'Card',
  system: 'http://hl7.org/fhir/udi-entry-type',
};
const udi_entry_type_Manual: Coding = {
  code: 'manual',
  display: 'Manual',
  system: 'http://hl7.org/fhir/udi-entry-type',
};
const udi_entry_type_RFID: Coding = {
  code: 'rfid',
  display: 'RFID',
  system: 'http://hl7.org/fhir/udi-entry-type',
};
const udi_entry_type_SelfReported: Coding = {
  code: 'self-reported',
  display: 'Self Reported',
  system: 'http://hl7.org/fhir/udi-entry-type',
};
const udi_entry_type_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://hl7.org/fhir/udi-entry-type',
};
/**
 * Codes to identify how UDI data was entered.
 */
export const UdiEntryType = {
  /**
   * a barcodescanner captured the data from the device label.
   */
  Barcode: udi_entry_type_Barcode,
  /**
   * The data originated from a patient's implant card and was read by an operator.
   */
  Card: udi_entry_type_Card,
  /**
   * The data was read from the label by a person and manually entered. (e.g.  via a keyboard).
   */
  Manual: udi_entry_type_Manual,
  /**
   * An RFID chip reader captured the data from the device label.
   */
  RFID: udi_entry_type_RFID,
  /**
   * The data originated from a patient source and was not directly scanned or read from a label or card.
   */
  SelfReported: udi_entry_type_SelfReported,
  /**
   * The method of data capture has not been determined.
   */
  Unknown: udi_entry_type_Unknown,
};
/**
 * A unit of time (units from UCUM).
 */
export const UnitsOfTime = {
  Years: AgeUnits_Years,
  Days: AgeUnits_Days,
  Hours: AgeUnits_Hours,
  Minutes: AgeUnits_Minutes,
  Months: AgeUnits_Months,
  Seconds: DurationUnits_Seconds,
  Weeks: AgeUnits_Weeks,
};
const unknown_content_code_UnknownElementsAndExtensions: Coding = {
  code: 'both',
  display: 'Unknown Elements and Extensions',
  system: 'http://hl7.org/fhir/unknown-content-code',
};
const unknown_content_code_UnknownElements: Coding = {
  code: 'elements',
  display: 'Unknown Elements',
  system: 'http://hl7.org/fhir/unknown-content-code',
};
const unknown_content_code_UnknownExtensions: Coding = {
  code: 'extensions',
  display: 'Unknown Extensions',
  system: 'http://hl7.org/fhir/unknown-content-code',
};
const unknown_content_code_NeitherElementsOrExtensions: Coding = {
  code: 'no',
  display: 'Neither Elements or Extensions',
  system: 'http://hl7.org/fhir/unknown-content-code',
};
/**
 * A code that indicates whether an application accepts unknown elements or extensions when reading resources.
 */
export const UnknownContentCode = {
  /**
   * The application accepts unknown elements and extensions.
   */
  UnknownElementsAndExtensions: unknown_content_code_UnknownElementsAndExtensions,
  /**
   * The application accepts unknown elements, but not unknown extensions.
   */
  UnknownElements: unknown_content_code_UnknownElements,
  /**
   * The application accepts unknown extensions, but not unknown elements.
   */
  UnknownExtensions: unknown_content_code_UnknownExtensions,
  /**
   * The application does not accept either unknown elements or extensions.
   */
  NeitherElementsOrExtensions: unknown_content_code_NeitherElementsOrExtensions,
};
const usage_context_type_AgeRange: Coding = {
  code: 'age',
  display: 'Age Range',
  system: 'http://terminology.hl7.org/CodeSystem/usage-context-type',
};
const usage_context_type_ClinicalFocus: Coding = {
  code: 'focus',
  display: 'Clinical Focus',
  system: 'http://terminology.hl7.org/CodeSystem/usage-context-type',
};
const usage_context_type_Gender: Coding = {
  code: 'gender',
  display: 'Gender',
  system: 'http://terminology.hl7.org/CodeSystem/usage-context-type',
};
const usage_context_type_Program: Coding = {
  code: 'program',
  display: 'Program',
  system: 'http://terminology.hl7.org/CodeSystem/usage-context-type',
};
const usage_context_type_Species: Coding = {
  code: 'species',
  display: 'Species',
  system: 'http://terminology.hl7.org/CodeSystem/usage-context-type',
};
const usage_context_type_WorkflowTask: Coding = {
  code: 'task',
  display: 'Workflow Task',
  system: 'http://terminology.hl7.org/CodeSystem/usage-context-type',
};
const usage_context_type_UserType: Coding = {
  code: 'user',
  display: 'User Type',
  system: 'http://terminology.hl7.org/CodeSystem/usage-context-type',
};
const usage_context_type_ClinicalVenue: Coding = {
  code: 'venue',
  display: 'Clinical Venue',
  system: 'http://terminology.hl7.org/CodeSystem/usage-context-type',
};
const usage_context_type_WorkflowSetting: Coding = {
  code: 'workflow',
  display: 'Workflow Setting',
  system: 'http://terminology.hl7.org/CodeSystem/usage-context-type',
};
/**
 * A code that specifies a type of context being specified by a usage context.
 */
export const UsageContextType = {
  /**
   * The age of the patient. For this context type, the value could be a range that specifies the applicable ages or a code from an appropriate value set such as the MeSH value set http://terminology.hl7.org/ValueSet/v3-AgeGroupObservationValue.
   */
  AgeRange: usage_context_type_AgeRange,
  /**
   * The clinical concept(s) addressed by the artifact. For example, disease, diagnostic test interpretation, medication ordering as in http://hl7.org/fhir/ValueSet/condition-code.
   */
  ClinicalFocus: usage_context_type_ClinicalFocus,
  /**
   * The gender of the patient. For this context type, appropriate values can be found in the http://hl7.org/fhir/ValueSet/administrative-gender value set.
   */
  Gender: usage_context_type_Gender,
  /**
   * A program/project of work for which this artifact is applicable.
   */
  Program: usage_context_type_Program,
  /**
   * The species to which an artifact applies. For example, SNOMED - 387961004 | Kingdom Animalia (organism).
   */
  Species: usage_context_type_Species,
  /**
   * The context for the clinical task(s) represented by this artifact. For example, this could be any task context represented by the HL7 ActTaskCode value set http://terminology.hl7.org/ValueSet/v3-ActTaskCode. General categories include: order entry, patient documentation and patient information review.
   */
  WorkflowTask: usage_context_type_WorkflowTask,
  /**
   * The clinical specialty of the context in which the patient is being treated - For example, PCP, Patient, Cardiologist, Behavioral Professional, Oral Health Professional, Prescriber, etc... taken from a specialty value set such as the NUCC Health Care provider taxonomy value set http://hl7.org/fhir/ValueSet/provider-taxonomy.
   */
  UserType: usage_context_type_UserType,
  /**
   * The venue in which an artifact could be used. For example, Outpatient, Inpatient, Home, Nursing home. The code value may originate from the HL7 ServiceDeliveryLocationRoleType value set (http://terminology.hl7.org/ValueSet/v3-ServiceDeliveryLocationRoleType).
   */
  ClinicalVenue: usage_context_type_ClinicalVenue,
  /**
   * The settings in which the artifact is intended for use. For example, admission, pre-op, etc. For example, the ActEncounterCode value set http://terminology.hl7.org/ValueSet/v3-ActEncounterCode.
   */
  WorkflowSetting: usage_context_type_WorkflowSetting,
};
const variable_type_Continuous: Coding = {
  code: 'continuous',
  display: 'Continuous',
  system: 'http://hl7.org/fhir/variable-type',
};
const variable_type_Descriptive: Coding = {
  code: 'descriptive',
  display: 'Descriptive',
  system: 'http://hl7.org/fhir/variable-type',
};
const variable_type_Dichotomous: Coding = {
  code: 'dichotomous',
  display: 'Dichotomous',
  system: 'http://hl7.org/fhir/variable-type',
};
/**
 * The possible types of variables for exposures or outcomes (E.g. Dichotomous, Continuous, Descriptive).
 */
export const VariableType = {
  /**
   * The variable is a continuous result such as a quantity.
   */
  Continuous: variable_type_Continuous,
  /**
   * The variable is described narratively rather than quantitatively.
   */
  Descriptive: variable_type_Descriptive,
  /**
   * The variable is dichotomous, such as present or absent.
   */
  Dichotomous: variable_type_Dichotomous,
};
const variant_state_Absent: Coding = {
  code: 'absent',
  display: 'absent',
  system: 'http://terminology.hl7.org/CodeSystem/variant-state',
};
const variant_state_Negative: Coding = {
  code: 'negative',
  display: 'negative',
  system: 'http://terminology.hl7.org/CodeSystem/variant-state',
};
const variant_state_Positive: Coding = {
  code: 'positive',
  display: 'positive',
  system: 'http://terminology.hl7.org/CodeSystem/variant-state',
};
/**
 * Codes providing the status of the variant test result.
 */
export const VariantState = {
  /**
   * result of the variant is missing.
   */
  Absent: variant_state_Absent,
  /**
   * no variant is detected.
   */
  Negative: variant_state_Negative,
  /**
   * the variant is detected.
   */
  Positive: variant_state_Positive,
};
const verificationresult_can_push_updates_No: Coding = {
  code: 'no',
  display: 'No',
  system: 'http://terminology.hl7.org/CodeSystem/can-push-updates',
};
const verificationresult_can_push_updates_Undetermined: Coding = {
  code: 'undetermined',
  display: 'Undetermined',
  system: 'http://terminology.hl7.org/CodeSystem/can-push-updates',
};
const verificationresult_can_push_updates_Yes: Coding = {
  code: 'yes',
  display: 'Yes',
  system: 'http://terminology.hl7.org/CodeSystem/can-push-updates',
};
/**
 * Ability of the primary source to push updates/alerts
 */
export const VerificationresultCanPushUpdates = {
  No: verificationresult_can_push_updates_No,
  Undetermined: verificationresult_can_push_updates_Undetermined,
  Yes: verificationresult_can_push_updates_Yes,
};
const verificationresult_communication_method_Manual: Coding = {
  code: 'manual',
  display: 'Manual',
  system: 'http://terminology.hl7.org/CodeSystem/verificationresult-communication-method',
};
const verificationresult_communication_method_Portal: Coding = {
  code: 'portal',
  display: 'Portal',
  system: 'http://terminology.hl7.org/CodeSystem/verificationresult-communication-method',
};
const verificationresult_communication_method_Pull: Coding = {
  code: 'pull',
  display: 'Pull',
  system: 'http://terminology.hl7.org/CodeSystem/verificationresult-communication-method',
};
const verificationresult_communication_method_Push: Coding = {
  code: 'push',
  display: 'Push',
  system: 'http://terminology.hl7.org/CodeSystem/verificationresult-communication-method',
};
/**
 * Attested information may be validated by process that are manual or automated. For automated processes it may accomplished by the system of record reaching out through another system's API or information may be sent to the system of record. This value set defines a set of codes to describing the process, the how, a resource or data element is validated.
 */
export const VerificationresultCommunicationMethod = {
  /**
   * The information is submitted/retrieved manually (e.g. by phone, fax, paper-based)
   */
  Manual: verificationresult_communication_method_Manual,
  /**
   * The information is submitted/retrieved via a portal
   */
  Portal: verificationresult_communication_method_Portal,
  /**
   * The information is retrieved (i.e. pulled) from a source (e.g. over an API)
   */
  Pull: verificationresult_communication_method_Pull,
  /**
   * The information is sent (i.e. pushed) from a source (e.g. over an API, asynchronously, secure messaging)
   */
  Push: verificationresult_communication_method_Push,
};
const verificationresult_failure_action_Fatal: Coding = {
  code: 'fatal',
  display: 'Fatal',
  system: 'http://terminology.hl7.org/CodeSystem/failure-action',
};
const verificationresult_failure_action_None: Coding = {
  code: 'none',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/failure-action',
};
const verificationresult_failure_action_RecordOnly: Coding = {
  code: 'rec-only',
  display: 'Record only',
  system: 'http://terminology.hl7.org/CodeSystem/failure-action',
};
const verificationresult_failure_action_Warning: Coding = {
  code: 'warn',
  display: 'Warning',
  system: 'http://terminology.hl7.org/CodeSystem/failure-action',
};
/**
 * The result if validation fails
 */
export const VerificationresultFailureAction = {
  Fatal: verificationresult_failure_action_Fatal,
  None: verificationresult_failure_action_None,
  RecordOnly: verificationresult_failure_action_RecordOnly,
  Warning: verificationresult_failure_action_Warning,
};
const verificationresult_need_Initial: Coding = {
  code: 'initial',
  display: 'Initial',
  system: 'http://terminology.hl7.org/CodeSystem/need',
};
const verificationresult_need_None: Coding = {
  code: 'none',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/need',
};
const verificationresult_need_Periodic: Coding = {
  code: 'periodic',
  display: 'Periodic',
  system: 'http://terminology.hl7.org/CodeSystem/need',
};
/**
 * The frequency with which the target must be validated
 */
export const VerificationresultNeed = {
  /**
   * ***TODO***
   */
  Initial: verificationresult_need_Initial,
  /**
   * ***TODO***
   */
  None: verificationresult_need_None,
  /**
   * ***TODO***
   */
  Periodic: verificationresult_need_Periodic,
};
const verificationresult_primary_source_type_AuthoritativeSource: Coding = {
  code: 'auth-source',
  display: 'Authoritative source',
  system: 'http://terminology.hl7.org/CodeSystem/primary-source-type',
};
const verificationresult_primary_source_type_ContinuingEducation: Coding = {
  code: 'cont-ed',
  display: 'Continuing Education',
  system: 'http://terminology.hl7.org/CodeSystem/primary-source-type',
};
const verificationresult_primary_source_type_IssuingSource: Coding = {
  code: 'issuer',
  display: 'Issuing source',
  system: 'http://terminology.hl7.org/CodeSystem/primary-source-type',
};
const verificationresult_primary_source_type_LegalSource: Coding = {
  code: 'legal',
  display: 'Legal source',
  system: 'http://terminology.hl7.org/CodeSystem/primary-source-type',
};
const verificationresult_primary_source_type_LicenseBoard: Coding = {
  code: 'lic-board',
  display: 'License Board',
  system: 'http://terminology.hl7.org/CodeSystem/primary-source-type',
};
const verificationresult_primary_source_type_PostalService: Coding = {
  code: 'post-serv',
  display: 'Postal Service',
  system: 'http://terminology.hl7.org/CodeSystem/primary-source-type',
};
const verificationresult_primary_source_type_PrimaryEducation: Coding = {
  code: 'prim',
  display: 'Primary Education',
  system: 'http://terminology.hl7.org/CodeSystem/primary-source-type',
};
const verificationresult_primary_source_type_RegistrationAuthority: Coding = {
  code: 'reg-auth',
  display: 'Registration Authority',
  system: 'http://terminology.hl7.org/CodeSystem/primary-source-type',
};
const verificationresult_primary_source_type_RelationshipOwner: Coding = {
  code: 'rel-own',
  display: 'Relationship owner',
  system: 'http://terminology.hl7.org/CodeSystem/primary-source-type',
};
/**
 * Type of the validation primary source
 */
export const VerificationresultPrimarySourceType = {
  AuthoritativeSource: verificationresult_primary_source_type_AuthoritativeSource,
  ContinuingEducation: verificationresult_primary_source_type_ContinuingEducation,
  IssuingSource: verificationresult_primary_source_type_IssuingSource,
  LegalSource: verificationresult_primary_source_type_LegalSource,
  LicenseBoard: verificationresult_primary_source_type_LicenseBoard,
  PostalService: verificationresult_primary_source_type_PostalService,
  PrimaryEducation: verificationresult_primary_source_type_PrimaryEducation,
  RegistrationAuthority: verificationresult_primary_source_type_RegistrationAuthority,
  RelationshipOwner: verificationresult_primary_source_type_RelationshipOwner,
};
const verificationresult_push_type_available_AnyChanges: Coding = {
  code: 'any',
  display: 'Any changes',
  system: 'http://terminology.hl7.org/CodeSystem/push-type-available',
};
const verificationresult_push_type_available_AsDefinedBySource: Coding = {
  code: 'source',
  display: 'As defined by source',
  system: 'http://terminology.hl7.org/CodeSystem/push-type-available',
};
const verificationresult_push_type_available_SpecificRequestedChanges: Coding = {
  code: 'specific',
  display: 'Specific requested changes',
  system: 'http://terminology.hl7.org/CodeSystem/push-type-available',
};
/**
 * Type of alerts/updates the primary source can send
 */
export const VerificationresultPushTypeAvailable = {
  AnyChanges: verificationresult_push_type_available_AnyChanges,
  AsDefinedBySource: verificationresult_push_type_available_AsDefinedBySource,
  SpecificRequestedChanges: verificationresult_push_type_available_SpecificRequestedChanges,
};
const verificationresult_status_Attested: Coding = {
  code: 'attested',
  display: 'Attested',
  system: 'http://hl7.org/fhir/CodeSystem/status',
};
const verificationresult_status_InProcess: Coding = {
  code: 'in-process',
  display: 'In process',
  system: 'http://hl7.org/fhir/CodeSystem/status',
};
const verificationresult_status_RequiresRevalidation: Coding = {
  code: 'req-revalid',
  display: 'Requires revalidation',
  system: 'http://hl7.org/fhir/CodeSystem/status',
};
const verificationresult_status_ReValidationFailed: Coding = {
  code: 'reval-fail',
  display: 'Re-Validation failed',
  system: 'http://hl7.org/fhir/CodeSystem/status',
};
const verificationresult_status_ValidationFailed: Coding = {
  code: 'val-fail',
  display: 'Validation failed',
  system: 'http://hl7.org/fhir/CodeSystem/status',
};
const verificationresult_status_Validated: Coding = {
  code: 'validated',
  display: 'Validated',
  system: 'http://hl7.org/fhir/CodeSystem/status',
};
/**
 * The validation status of the target
 */
export const VerificationresultStatus = {
  /**
   * ***TODO***
   */
  Attested: verificationresult_status_Attested,
  /**
   * ***TODO***
   */
  InProcess: verificationresult_status_InProcess,
  /**
   * ***TODO***
   */
  RequiresRevalidation: verificationresult_status_RequiresRevalidation,
  /**
   * ***TODO***
   */
  ReValidationFailed: verificationresult_status_ReValidationFailed,
  /**
   * ***TODO***
   */
  ValidationFailed: verificationresult_status_ValidationFailed,
  /**
   * ***TODO***
   */
  Validated: verificationresult_status_Validated,
};
const verificationresult_validation_process_EditCheck: Coding = {
  code: 'edit-check',
  display: 'edit check',
  system: 'http://terminology.hl7.org/CodeSystem/validation-process',
};
const verificationresult_validation_process_InContext: Coding = {
  code: 'in-context',
  display: 'in context',
  system: 'http://terminology.hl7.org/CodeSystem/validation-process',
};
const verificationresult_validation_process_MultipleSources: Coding = {
  code: 'multi',
  display: 'multiple sources',
  system: 'http://terminology.hl7.org/CodeSystem/validation-process',
};
const verificationresult_validation_process_PrimarySource: Coding = {
  code: 'primary',
  display: 'primary source',
  system: 'http://terminology.hl7.org/CodeSystem/validation-process',
};
const verificationresult_validation_process_Standalone: Coding = {
  code: 'standalone',
  display: 'standalone',
  system: 'http://terminology.hl7.org/CodeSystem/validation-process',
};
const verificationresult_validation_process_ValueSet: Coding = {
  code: 'valueset',
  display: 'value set',
  system: 'http://terminology.hl7.org/CodeSystem/validation-process',
};
/**
 * The primary process by which the target is validated
 */
export const VerificationresultValidationProcess = {
  EditCheck: verificationresult_validation_process_EditCheck,
  InContext: verificationresult_validation_process_InContext,
  MultipleSources: verificationresult_validation_process_MultipleSources,
  PrimarySource: verificationresult_validation_process_PrimarySource,
  Standalone: verificationresult_validation_process_Standalone,
  ValueSet: verificationresult_validation_process_ValueSet,
};
const verificationresult_validation_status_Failed: Coding = {
  code: 'failed',
  display: 'Failed',
  system: 'http://terminology.hl7.org/CodeSystem/validation-status',
};
const verificationresult_validation_status_Successful: Coding = {
  code: 'successful',
  display: 'Successful',
  system: 'http://terminology.hl7.org/CodeSystem/validation-status',
};
const verificationresult_validation_status_Unknown: Coding = {
  code: 'unknown',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/validation-status',
};
/**
 * Status of the validation of the target against the primary source
 */
export const VerificationresultValidationStatus = {
  Failed: verificationresult_validation_status_Failed,
  Successful: verificationresult_validation_status_Successful,
  /**
   * The validations status has not been determined yet
   */
  Unknown: verificationresult_validation_status_Unknown,
};
const verificationresult_validation_type_MultipleSources: Coding = {
  code: 'multiple',
  display: 'Multiple Sources',
  system: 'http://terminology.hl7.org/CodeSystem/validation-type',
};
const verificationresult_validation_type_Nothing: Coding = {
  code: 'nothing',
  display: 'Nothing',
  system: 'http://terminology.hl7.org/CodeSystem/validation-type',
};
const verificationresult_validation_type_PrimarySource: Coding = {
  code: 'primary',
  display: 'Primary Source',
  system: 'http://terminology.hl7.org/CodeSystem/validation-type',
};
/**
 * What the target is validated against
 */
export const VerificationresultValidationType = {
  MultipleSources: verificationresult_validation_type_MultipleSources,
  Nothing: verificationresult_validation_type_Nothing,
  PrimarySource: verificationresult_validation_type_PrimarySource,
};
const versioning_policy_NoVersionIdSupport: Coding = {
  code: 'no-version',
  display: 'No VersionId Support',
  system: 'http://hl7.org/fhir/versioning-policy',
};
const versioning_policy_Versioned: Coding = {
  code: 'versioned',
  display: 'Versioned',
  system: 'http://hl7.org/fhir/versioning-policy',
};
const versioning_policy_VersionIdTrackedFully: Coding = {
  code: 'versioned-update',
  display: 'VersionId tracked fully',
  system: 'http://hl7.org/fhir/versioning-policy',
};
/**
 * How the system supports versioning for a resource.
 */
export const VersioningPolicy = {
  /**
   * VersionId meta-property is not supported (server) or used (client).
   */
  NoVersionIdSupport: versioning_policy_NoVersionIdSupport,
  /**
   * VersionId meta-property is supported (server) or used (client).
   */
  Versioned: versioning_policy_Versioned,
  /**
   * VersionId must be correct for updates (server) or will be specified (If-match header) for updates (client).
   */
  VersionIdTrackedFully: versioning_policy_VersionIdTrackedFully,
};
const vision_base_codes_Down: Coding = {
  code: 'down',
  display: 'Down',
  system: 'http://hl7.org/fhir/vision-base-codes',
};
const vision_base_codes_In: Coding = {
  code: 'in',
  display: 'In',
  system: 'http://hl7.org/fhir/vision-base-codes',
};
const vision_base_codes_Out: Coding = {
  code: 'out',
  display: 'Out',
  system: 'http://hl7.org/fhir/vision-base-codes',
};
const vision_base_codes_Up: Coding = {
  code: 'up',
  display: 'Up',
  system: 'http://hl7.org/fhir/vision-base-codes',
};
/**
 * A coded concept listing the base codes.
 */
export const VisionBaseCodes = {
  /**
   * bottom.
   */
  Down: vision_base_codes_Down,
  /**
   * inner edge.
   */
  In: vision_base_codes_In,
  /**
   * outer edge.
   */
  Out: vision_base_codes_Out,
  /**
   * top.
   */
  Up: vision_base_codes_Up,
};
const vision_eye_codes_LeftEye: Coding = {
  code: 'left',
  display: 'Left Eye',
  system: 'http://hl7.org/fhir/vision-eye-codes',
};
const vision_eye_codes_RightEye: Coding = {
  code: 'right',
  display: 'Right Eye',
  system: 'http://hl7.org/fhir/vision-eye-codes',
};
/**
 * A coded concept listing the eye codes.
 */
export const VisionEyeCodes = {
  /**
   * Left Eye.
   */
  LeftEye: vision_eye_codes_LeftEye,
  /**
   * Right Eye.
   */
  RightEye: vision_eye_codes_RightEye,
};
const vision_product_ContactLens: Coding = {
  code: 'contact',
  display: 'Contact Lens',
  system: 'http://terminology.hl7.org/CodeSystem/ex-visionprescriptionproduct',
};
const vision_product_Lens: Coding = {
  code: 'lens',
  display: 'Lens',
  system: 'http://terminology.hl7.org/CodeSystem/ex-visionprescriptionproduct',
};
/**
 * This value set includes a smattering of Prescription Product codes.
 */
export const VisionProduct = {
  /**
   * A lens to be fitted for wearing directly on an eye.
   */
  ContactLens: vision_product_ContactLens,
  /**
   * A lens to be fitted to a frame to comprise a pair of glasses.
   */
  Lens: vision_product_Lens,
};
/**
 * This value set includes common codes from BCP-47 (http://tools.ietf.org/html/bcp47) for the purpose of writing; this value set (unlike the common languages value set) doesn't include dialects except where they are relevant for written languages
 */
export const WrittenLanguage = {
  Arabic: Languages_Arabic,
  Bengali: Languages_Bengali,
  Czech: Languages_Czech,
  Danish: Languages_Danish,
  German: Languages_German,
  Greek: Languages_Greek,
  English: Languages_English,
  Spanish: Languages_Spanish,
  Finnish: Languages_Finnish,
  French: Languages_French,
  Frysian: Languages_Frysian,
  Hindi: Languages_Hindi,
  Croatian: Languages_Croatian,
  Italian: Languages_Italian,
  Japanese: Languages_Japanese,
  Korean: Languages_Korean,
  Dutch: Languages_Dutch,
  Norwegian: Languages_Norwegian,
  Punjabi: Languages_Punjabi,
  Polish: Languages_Polish,
  Portuguese: Languages_Portuguese,
  Russian: Languages_Russian,
  Serbian: Languages_Serbian,
  Swedish: Languages_Swedish,
  Telegu: Languages_Telegu,
  Chinese: Languages_Chinese,
};
const v2_0136_No: Coding = {
  code: 'N',
  display: 'No',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0136',
};
const v2_0136_Yes: Coding = {
  code: 'Y',
  display: 'Yes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0136',
};
/**
 * For Capturing simple yes-no-don't know answers
 */
export const Yesnodontknow = {
  /**
   * The source was asked but does not know the value.
   */
  AskedButUnknown_data_absent_reason: data_absent_reason_AskedButUnknown,
  No_v2_0136: v2_0136_No,
  Yes_v2_0136: v2_0136_Yes,
};
const v2_0001_Ambiguous: Coding = {
  code: 'A',
  display: 'Ambiguous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0001',
};
const v2_0001_Female: Coding = {
  code: 'F',
  display: 'Female',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0001',
};
const v2_0001_Male: Coding = {
  code: 'M',
  display: 'Male',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0001',
};
const v2_0001_NotApplicable: Coding = {
  code: 'N',
  display: 'Not applicable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0001',
};
const v2_0001_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0001',
};
const v2_0001_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0001',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0001 ( SEX)
 */
export const V20001 = {
  Ambiguous: v2_0001_Ambiguous,
  Female: v2_0001_Female,
  Male: v2_0001_Male,
  NotApplicable: v2_0001_NotApplicable,
  Other: v2_0001_Other,
  Unknown: v2_0001_Unknown,
};
const v2_0002_Separated: Coding = {
  code: 'A',
  display: 'Separated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_Unmarried: Coding = {
  code: 'B',
  display: 'Unmarried',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_CommonLaw: Coding = {
  code: 'C',
  display: 'Common law',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_Divorced: Coding = {
  code: 'D',
  display: 'Divorced',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_LegallySeparated: Coding = {
  code: 'E',
  display: 'Legally Separated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_LivingTogether: Coding = {
  code: 'G',
  display: 'Living together',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_Interlocutory: Coding = {
  code: 'I',
  display: 'Interlocutory',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_Married: Coding = {
  code: 'M',
  display: 'Married',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_Annulled: Coding = {
  code: 'N',
  display: 'Annulled',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_DomesticPartner: Coding = {
  code: 'P',
  display: 'Domestic partner',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_RegisteredDomesticPartner: Coding = {
  code: 'R',
  display: 'Registered domestic partner',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_Single: Coding = {
  code: 'S',
  display: 'Single',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_Unreported: Coding = {
  code: 'T',
  display: 'Unreported',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
const v2_0002_Widowed: Coding = {
  code: 'W',
  display: 'Widowed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0002',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0002 ( MARITAL STATUS)
 */
export const V20002 = {
  Separated: v2_0002_Separated,
  Unmarried: v2_0002_Unmarried,
  CommonLaw: v2_0002_CommonLaw,
  Divorced: v2_0002_Divorced,
  LegallySeparated: v2_0002_LegallySeparated,
  LivingTogether: v2_0002_LivingTogether,
  Interlocutory: v2_0002_Interlocutory,
  Married: v2_0002_Married,
  Annulled: v2_0002_Annulled,
  Other: v2_0002_Other,
  DomesticPartner: v2_0002_DomesticPartner,
  RegisteredDomesticPartner: v2_0002_RegisteredDomesticPartner,
  Single: v2_0002_Single,
  Unreported: v2_0002_Unreported,
  Unknown: v2_0002_Unknown,
  Widowed: v2_0002_Widowed,
};
const v2_0003_ADTACKAdmitVisitNotification: Coding = {
  code: 'A01',
  display: 'ADT/ACK - Admit/visit notification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKTransferAPatient: Coding = {
  code: 'A02',
  display: 'ADT/ACK - Transfer a patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKDischargeEndVisit: Coding = {
  code: 'A03',
  display: 'ADT/ACK -  Discharge/end visit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKRegisterAPatient: Coding = {
  code: 'A04',
  display: 'ADT/ACK -  Register a patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKPreAdmitAPatient: Coding = {
  code: 'A05',
  display: 'ADT/ACK -  Pre-admit a patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKChangeAnOutpatientToAnInpatient: Coding = {
  code: 'A06',
  display: 'ADT/ACK -  Change an outpatient to an inpatient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKChangeAnInpatientToAnOutpatient: Coding = {
  code: 'A07',
  display: 'ADT/ACK -  Change an inpatient to an outpatient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKUpdatePatientInformation: Coding = {
  code: 'A08',
  display: 'ADT/ACK -  Update patient information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKPatientDepartingTracking: Coding = {
  code: 'A09',
  display: 'ADT/ACK -  Patient departing - tracking',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKPatientArrivingTracking: Coding = {
  code: 'A10',
  display: 'ADT/ACK -  Patient arriving - tracking',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKCancelAdmitVisitNotification: Coding = {
  code: 'A11',
  display: 'ADT/ACK -  Cancel admit/visit notification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKCancelTransfer: Coding = {
  code: 'A12',
  display: 'ADT/ACK -  Cancel transfer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKCancelDischargeEndVisit: Coding = {
  code: 'A13',
  display: 'ADT/ACK -  Cancel discharge/end visit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKPendingAdmit: Coding = {
  code: 'A14',
  display: 'ADT/ACK -  Pending admit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKPendingTransfer: Coding = {
  code: 'A15',
  display: 'ADT/ACK -  Pending transfer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKPendingDischarge: Coding = {
  code: 'A16',
  display: 'ADT/ACK -  Pending discharge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKSwapPatients: Coding = {
  code: 'A17',
  display: 'ADT/ACK -  Swap patients',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKBedStatusUpdate: Coding = {
  code: 'A20',
  display: 'ADT/ACK -  Bed status update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKPatientGoesOnAQuotationLeaveOfAbsenceQuotation: Coding = {
  code: 'A21',
  display: 'ADT/ACK -  Patient goes on a "leave of absence"',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKPatientReturnsFromAQuotationLeaveOfAbsenceQuotation: Coding = {
  code: 'A22',
  display: 'ADT/ACK -  Patient returns from a "leave of absence"',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKDeleteAPatientRecord: Coding = {
  code: 'A23',
  display: 'ADT/ACK -  Delete a patient record',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKLinkPatientInformation: Coding = {
  code: 'A24',
  display: 'ADT/ACK -  Link patient information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKCancelPendingDischarge: Coding = {
  code: 'A25',
  display: 'ADT/ACK -  Cancel pending discharge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKCancelPendingTransfer: Coding = {
  code: 'A26',
  display: 'ADT/ACK -  Cancel pending transfer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKCancelPendingAdmit: Coding = {
  code: 'A27',
  display: 'ADT/ACK -  Cancel pending admit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKAddPersonInformation: Coding = {
  code: 'A28',
  display: 'ADT/ACK -  Add person information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKDeletePersonInformation: Coding = {
  code: 'A29',
  display: 'ADT/ACK -  Delete person information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKUpdatePersonInformation: Coding = {
  code: 'A31',
  display: 'ADT/ACK -  Update person information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKCancelPatientArrivingTracking: Coding = {
  code: 'A32',
  display: 'ADT/ACK -  Cancel patient arriving - tracking',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKCancelPatientDepartingTracking: Coding = {
  code: 'A33',
  display: 'ADT/ACK -  Cancel patient departing - tracking',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKUnlinkPatientInformation: Coding = {
  code: 'A37',
  display: 'ADT/ACK -  Unlink patient information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKCancelPreAdmit: Coding = {
  code: 'A38',
  display: 'ADT/ACK - Cancel pre-admit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKMergePatientPatientIdentifierList: Coding = {
  code: 'A40',
  display: 'ADT/ACK - Merge patient - patient identifier list',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKMergeAccountPatientAccountNumber: Coding = {
  code: 'A41',
  display: 'ADT/ACK - Merge account - patient account number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKMergeVisitVisitNumber: Coding = {
  code: 'A42',
  display: 'ADT/ACK - Merge visit - visit number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKMovePatientInformationPatientIdentifierList: Coding = {
  code: 'A43',
  display: 'ADT/ACK - Move patient information - patient identifier list',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKMoveAccountInformationPatientAccountNumber: Coding = {
  code: 'A44',
  display: 'ADT/ACK - Move account information - patient account number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKMoveVisitInformationVisitNumber: Coding = {
  code: 'A45',
  display: 'ADT/ACK - Move visit information - visit number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKChangePatientIdentifierList: Coding = {
  code: 'A47',
  display: 'ADT/ACK - Change patient identifier list',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKChangePatientAccountNumber: Coding = {
  code: 'A49',
  display: 'ADT/ACK - Change patient account number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKChangeVisitNumber: Coding = {
  code: 'A50',
  display: 'ADT/ACK - Change visit number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKChangeAlternateVisitID: Coding = {
  code: 'A51',
  display: 'ADT/ACK - Change alternate visit ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKCancelLeaveOfAbsenceForAPatient: Coding = {
  code: 'A52',
  display: 'ADT/ACK - Cancel leave of absence for a patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKCancelPatientReturnsFromALeaveOfAbsence: Coding = {
  code: 'A53',
  display: 'ADT/ACK - Cancel patient returns from a leave of absence',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKChangeAttendingDoctor: Coding = {
  code: 'A54',
  display: 'ADT/ACK - Change attending doctor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKCancelChangeAttendingDoctor: Coding = {
  code: 'A55',
  display: 'ADT/ACK - Cancel change attending doctor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKUpdateAllergyInformation: Coding = {
  code: 'A60',
  display: 'ADT/ACK - Update allergy information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKChangeConsultingDoctor: Coding = {
  code: 'A61',
  display: 'ADT/ACK - Change consulting doctor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ADTACKCancelChangeConsultingDoctor: Coding = {
  code: 'A62',
  display: 'ADT/ACK - Cancel change consulting doctor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PMUACKAddPersonnelRecord: Coding = {
  code: 'B01',
  display: 'PMU/ACK - Add personnel record',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PMUACKUpdatePersonnelRecord: Coding = {
  code: 'B02',
  display: 'PMU/ACK - Update personnel record',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PMUACKDeletePersonnelReCord: Coding = {
  code: 'B03',
  display: 'PMU/ACK - Delete personnel re cord',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PMUACKActivePracticingPerson: Coding = {
  code: 'B04',
  display: 'PMU/ACK - Active practicing person',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PMUACKDeactivatePracticingPerson: Coding = {
  code: 'B05',
  display: 'PMU/ACK - Deactivate practicing person',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PMUACKTerminatePracticingPerson: Coding = {
  code: 'B06',
  display: 'PMU/ACK - Terminate practicing person',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PMUACKGrantCertificatePermission: Coding = {
  code: 'B07',
  display: 'PMU/ACK - Grant Certificate/Permission',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PMUACKRevokeCertificatePermission: Coding = {
  code: 'B08',
  display: 'PMU/ACK - Revoke Certificate/Permission',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CRMRegisterAPatientOnAClinicalTrial: Coding = {
  code: 'C01',
  display: 'CRM - Register a patient on a clinical trial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CRMCancelAPatientRegistrationOnClinicalTrialForClericalMistakesOnly: Coding = {
  code: 'C02',
  display: 'CRM - Cancel a patient registration on clinical trial (for clerical mistakes only)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CRMCorrectUpdateRegistrationInformation: Coding = {
  code: 'C03',
  display: 'CRM - Correct/update registration information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CRMPatientHasGoneOffAClinicalTrial: Coding = {
  code: 'C04',
  display: 'CRM - Patient has gone off a clinical trial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CRMPatientEntersPhaseOfClinicalTrial: Coding = {
  code: 'C05',
  display: 'CRM - Patient enters phase of clinical trial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CRMCancelPatientEnteringAPhaseClericalMistake: Coding = {
  code: 'C06',
  display: 'CRM - Cancel patient entering a phase (clerical mistake)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CRMCorrectUpdatePhaseInformation: Coding = {
  code: 'C07',
  display: 'CRM - Correct/update phase information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CRMPatientHasGoneOffPhaseOfClinicalTrial: Coding = {
  code: 'C08',
  display: 'CRM - Patient has gone off phase of clinical trial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CSUAutomatedTimeIntervalsForReportingLikeMonthly: Coding = {
  code: 'C09',
  display: 'CSU - Automated time intervals for reporting, like monthly',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CSUPatientCompletesTheClinicalTrial: Coding = {
  code: 'C10',
  display: 'CSU - Patient completes the clinical trial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CSUPatientCompletesAPhaseOfTheClinicalTrial: Coding = {
  code: 'C11',
  display: 'CSU - Patient completes a phase of the clinical trial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CSUUpdateCorrectionOfPatientOrderResultInformation: Coding = {
  code: 'C12',
  display: 'CSU - Update/correction of patient order/result information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CancelQuery: Coding = {
  code: 'CNQ',
  display: 'Cancel Query',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SubmitHealthCareServicesInvoice: Coding = {
  code: 'E01',
  display: 'Submit HealthCare Services Invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CancelHealthCareServicesInvoice: Coding = {
  code: 'E02',
  display: 'Cancel HealthCare Services Invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_HealthCareServicesInvoiceStatus: Coding = {
  code: 'E03',
  display: 'HealthCare Services Invoice Status',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ReAssessHealthCareServicesInvoiceRequest: Coding = {
  code: 'E04',
  display: 'Re-Assess HealthCare Services Invoice Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_EditAdjudicationResults: Coding = {
  code: 'E10',
  display: 'Edit/Adjudication Results',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RequestAdditionalInformation: Coding = {
  code: 'E12',
  display: 'Request Additional Information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_AdditionalInformationResponse: Coding = {
  code: 'E13',
  display: 'Additional Information Response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PaymentRemittanceAdvice: Coding = {
  code: 'E15',
  display: 'Payment/Remittance Advice',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SubmitAuthorizationRequest: Coding = {
  code: 'E20',
  display: 'Submit Authorization Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CancelAuthorizationRequest: Coding = {
  code: 'E21',
  display: 'Cancel Authorization Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_AuthorizationRequestStatus: Coding = {
  code: 'E22',
  display: 'Authorization Request Status',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_AuthorizationResponse: Coding = {
  code: 'E24',
  display: 'Authorization Response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SubmitHealthDocumentRelatedToAuthorizationRequest: Coding = {
  code: 'E30',
  display: 'Submit Health Document related to Authorization Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CancelHealthDocumentRelatedToAuthorizationRequest: Coding = {
  code: 'E31',
  display: 'Cancel Health Document related to Authorization Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RQIRPIRequestForInsuranceInformation: Coding = {
  code: 'I01',
  display: 'RQI/RPI - Request for insurance information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RQIRPLRequestReceiptOfPatientSelectionDisplayList: Coding = {
  code: 'I02',
  display: 'RQI/RPL - Request/receipt of patient selection display list',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RQIRPRRequestReceiptOfPatientSelectionList: Coding = {
  code: 'I03',
  display: 'RQI/RPR - Request/receipt of patient selection list',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RQDRPIRequestForPatientDemographicData: Coding = {
  code: 'I04',
  display: 'RQD/RPI - Request for patient demographic data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PINACKUnsolicitedInsuranceInformation: Coding = {
  code: 'I07',
  display: 'PIN/ACK - Unsolicited insurance information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RQARPARequestForTreatmentAuthorizationInformation: Coding = {
  code: 'I08',
  display: 'RQA/RPA - Request for treatment authorization information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RQARPARequestForModificationToAnAuthorization: Coding = {
  code: 'I09',
  display: 'RQA/RPA - Request for modification to an authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RQARPARequestForResubmissionOfAnAuthorization: Coding = {
  code: 'I10',
  display: 'RQA/RPA - Request for resubmission of an authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RQARPARequestForCancellationOfAnAuthorization: Coding = {
  code: 'I11',
  display: 'RQA/RPA - Request for cancellation of an authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_REFRRIPatientReferral: Coding = {
  code: 'I12',
  display: 'REF/RRI - Patient referral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_REFRRIModifyPatientReferral: Coding = {
  code: 'I13',
  display: 'REF/RRI - Modify patient referral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_REFRRICancelPatientReferral: Coding = {
  code: 'I14',
  display: 'REF/RRI - Cancel patient referral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_REFRRIRequestPatientReferralStatus: Coding = {
  code: 'I15',
  display: 'REF/RRI - Request patient referral status',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CollaborativeCareReferral: Coding = {
  code: 'I16',
  display: 'Collaborative Care Referral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CollaborativeCareQueryCollaborativeCareQueryUpdate: Coding = {
  code: 'I19',
  display: 'Collaborative Care Query/Collaborative Care Query Update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_AsynchronousCollaborativeCareUpdate: Coding = {
  code: 'I20',
  display: 'Asynchronous Collaborative Care Update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CollaborativeCareMessage: Coding = {
  code: 'I21',
  display: 'Collaborative Care Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_CollaborativeCareFetchCollaborativeCareInformation: Coding = {
  code: 'I22',
  display: 'Collaborative Care Fetch / Collaborative Care Information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QCNACKCancelQueryAcknowledgeMessage: Coding = {
  code: 'J01',
  display: 'QCN/ACK - Cancel query/acknowledge message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QSXACKCancelSubscriptionAcknowledgeMessage: Coding = {
  code: 'J02',
  display: 'QSX/ACK - Cancel subscription/acknowledge message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RSPSegmentPatternResponseInResponseToQBPPowerQ11: Coding = {
  code: 'K11',
  display: 'RSP - Segment pattern response in response to QBP^Q11',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RTBTabularResponseInResponseToQBPPowerQ13: Coding = {
  code: 'K13',
  display: 'RTB - Tabular response in response to QBP^Q13',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RDYDisplayResponseInResponseToQBPPowerQ15: Coding = {
  code: 'K15',
  display: 'RDY - Display response in response to QBP^Q15',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RSPGetPersonDemographicsResponse: Coding = {
  code: 'K21',
  display: 'RSP - Get person demographics response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RSPFindCandidatesResponse: Coding = {
  code: 'K22',
  display: 'RSP - Find candidates response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RSPGetCorrespondingIdentifiersResponse: Coding = {
  code: 'K23',
  display: 'RSP - Get corresponding identifiers response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RSPAllocateIdentifiersResponse: Coding = {
  code: 'K24',
  display: 'RSP - Allocate identifiers response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RSPPersonnelInformationBySegmentResponse: Coding = {
  code: 'K25',
  display: 'RSP - Personnel Information by Segment Response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RSPDispenseHistoryResponse: Coding = {
  code: 'K31',
  display: 'RSP -Dispense History Response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_FindCandidatesIncludingVisitInformationResponse: Coding = {
  code: 'K32',
  display: 'Find Candidates including Visit Information Response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_GetDonorRecordCandidatesResponseMessage: Coding = {
  code: 'K33',
  display: 'Get Donor Record Candidates Response Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SegmentPatternResponseMessage: Coding = {
  code: 'K34',
  display: 'Segment Pattern Response Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKMasterFileStaffPractitioner: Coding = {
  code: 'M02',
  display: 'MFN/MFK - Master file - staff practitioner',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKMasterFilesChargeDescription: Coding = {
  code: 'M04',
  display: 'MFN/MFK - Master files charge description',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKPatientLocationMasterFile: Coding = {
  code: 'M05',
  display: 'MFN/MFK - Patient location master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKClinicalStudyWithPhasesAndSchedulesMasterFile: Coding = {
  code: 'M06',
  display: 'MFN/MFK - Clinical study with phases and schedules master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKClinicalStudyWithoutPhasesButWithSchedulesMasterFile: Coding = {
  code: 'M07',
  display: 'MFN/MFK - Clinical study without phases but with schedules master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKTestObservationNumericMasterFile: Coding = {
  code: 'M08',
  display: 'MFN/MFK - Test/observation (numeric) master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKTestObservationCategoricalMasterFile: Coding = {
  code: 'M09',
  display: 'MFN/MFK - Test/observation (categorical) master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKTestObservationBatteriesMasterFile: Coding = {
  code: 'M10',
  display: 'MFN/MFK - Test /observation batteries master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKTestCalculatedObservationsMasterFile: Coding = {
  code: 'M11',
  display: 'MFN/MFK - Test/calculated observations master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKMasterFileNotificationMessage: Coding = {
  code: 'M12',
  display: 'MFN/MFK - Master file notification message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKMasterFileNotificationGeneral: Coding = {
  code: 'M13',
  display: 'MFN/MFK - Master file notification - general',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKMasterFileNotificationSiteDefined: Coding = {
  code: 'M14',
  display: 'MFN/MFK - Master file notification - site defined',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKInventoryItemMasterFileNotification: Coding = {
  code: 'M15',
  display: 'MFN/MFK - Inventory item master file notification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKMasterFileNotificationInventoryItemEnhanced: Coding = {
  code: 'M16',
  display: 'MFN/MFK - Master File Notification Inventory Item Enhanced',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_DRGMasterFileMessage: Coding = {
  code: 'M17',
  display: 'DRG Master File Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFNMFKMasterFileNotificationTestObservationPayer: Coding = {
  code: 'M18',
  display: 'MFN/MFK - Master file notification - Test/Observation (Payer)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_NMDACKApplicationManagementDataMessageUnsolicited: Coding = {
  code: 'N02',
  display: 'NMD/ACK - Application management data message (unsolicited)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OMDDietOrder: Coding = {
  code: 'O03',
  display: 'OMD - Diet order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORDDietOrderAcknowledgment: Coding = {
  code: 'O04',
  display: 'ORD - Diet order acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OMSStockRequisitionOrder: Coding = {
  code: 'O05',
  display: 'OMS - Stock requisition order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORSStockRequisitionAcknowledgment: Coding = {
  code: 'O06',
  display: 'ORS - Stock requisition acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OMNNonStockRequisitionOrder: Coding = {
  code: 'O07',
  display: 'OMN - Non-stock requisition order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORNNonStockRequisitionAcknowledgment: Coding = {
  code: 'O08',
  display: 'ORN - Non-stock requisition acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OMPPharmacyTreatmentOrder: Coding = {
  code: 'O09',
  display: 'OMP - Pharmacy/treatment order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORPPharmacyTreatmentOrderAcknowledgment: Coding = {
  code: 'O10',
  display: 'ORP - Pharmacy/treatment order acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RDEPharmacyTreatmentEncodedOrder: Coding = {
  code: 'O11',
  display: 'RDE - Pharmacy/treatment encoded order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RREPharmacyTreatmentEncodedOrderAcknowledgment: Coding = {
  code: 'O12',
  display: 'RRE - Pharmacy/treatment encoded order acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RDSPharmacyTreatmentDispense: Coding = {
  code: 'O13',
  display: 'RDS - Pharmacy/treatment dispense',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RRDPharmacyTreatmentDispenseAcknowledgment: Coding = {
  code: 'O14',
  display: 'RRD - Pharmacy/treatment dispense acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RGVPharmacyTreatmentGive: Coding = {
  code: 'O15',
  display: 'RGV - Pharmacy/treatment give',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RRGPharmacyTreatmentGiveAcknowledgment: Coding = {
  code: 'O16',
  display: 'RRG - Pharmacy/treatment give acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RASPharmacyTreatmentAdministration: Coding = {
  code: 'O17',
  display: 'RAS - Pharmacy/treatment administration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RRAPharmacyTreatmentAdministrationAcknowledgment: Coding = {
  code: 'O18',
  display: 'RRA - Pharmacy/treatment administration acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OMGGeneralClinicalOrder: Coding = {
  code: 'O19',
  display: 'OMG - General clinical order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORGORLGeneralClinicalOrderResponse: Coding = {
  code: 'O20',
  display: 'ORG/ORL - General clinical order response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OMLLaboratoryOrder: Coding = {
  code: 'O21',
  display: 'OML - Laboratory order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORLGeneralLaboratoryOrderResponseMessageToAnyOML: Coding = {
  code: 'O22',
  display: 'ORL - General laboratory order response message to any OML',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OMIImagingOrder: Coding = {
  code: 'O23',
  display: 'OMI - Imaging order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORIImagingOrderResponseMessageToAnyOMI: Coding = {
  code: 'O24',
  display: 'ORI - Imaging order response message to any OMI',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RDEPharmacyTreatmentRefillAuthorizationRequest: Coding = {
  code: 'O25',
  display: 'RDE - Pharmacy/treatment refill authorization request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_RREPharmacyTreatmentRefillAuthorizationAcknowledgement: Coding = {
  code: 'O26',
  display: 'RRE - Pharmacy/Treatment Refill Authorization Acknowledgement',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OMBBloodProductOrder: Coding = {
  code: 'O27',
  display: 'OMB - Blood product order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORBBloodProductOrderAcknowledgment: Coding = {
  code: 'O28',
  display: 'ORB - Blood product order acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_BPSBloodProductDispenseStatus: Coding = {
  code: 'O29',
  display: 'BPS - Blood product dispense status',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_BRPBloodProductDispenseStatusAcknowledgment: Coding = {
  code: 'O30',
  display: 'BRP - Blood product dispense status acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_BTSBloodProductTransfusionDisposition: Coding = {
  code: 'O31',
  display: 'BTS - Blood product transfusion/disposition',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_BRTBloodProductTransfusionDispositionAcknowledgment: Coding = {
  code: 'O32',
  display: 'BRT - Blood product transfusion/disposition acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OMLLaboratoryOrderForMultipleOrdersRelatedToASingleSpecimen: Coding = {
  code: 'O33',
  display: 'OML - Laboratory order for multiple orders related to a single specimen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORLLaboratoryOrderResponseMessageToAMultipleOrderRelatedToSingleSpecimenOML: Coding = {
  code: 'O34',
  display: 'ORL - Laboratory order response message to a multiple order related to single specimen OML',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OMLLaboratoryOrderForMultipleOrdersRelatedToASingleContainerOfASpecimen: Coding = {
  code: 'O35',
  display: 'OML - Laboratory order for multiple orders related to a single container of a specimen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORLLaboratoryOrderResponseMessageToASingleContainerOfASpecimenOML: Coding = {
  code: 'O36',
  display: 'ORL - Laboratory order response message to a single container of a specimen OML',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OPLPopulationLocationBasedLaboratoryOrderMessage: Coding = {
  code: 'O37',
  display: 'OPL - Population/Location-Based Laboratory Order Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OPRPopulationLocationBasedLaboratoryOrderAcknowledgmentMessage: Coding = {
  code: 'O38',
  display: 'OPR - Population/Location-Based Laboratory Order Acknowledgment Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SpecimenShipmentCentricLaboratoryOrder: Coding = {
  code: 'O39',
  display: 'Specimen shipment centric laboratory order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SpecimenShipmentCentricLaboratoryOrderAcknowledgmentMessage: Coding = {
  code: 'O40',
  display: 'Specimen Shipment Centric Laboratory Order Acknowledgment Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_DBCCreateDonorRecordMessage: Coding = {
  code: 'O41',
  display: 'DBC - Create Donor Record Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_DBUUpdateDonorRecordMessage: Coding = {
  code: 'O42',
  display: 'DBU - Update Donor Record Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_GeneralOrderMessageWithDocumentPayloadAcknowledgementMessage: Coding = {
  code: 'O43',
  display: 'General Order Message with Document Payload Acknowledgement Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_DonorRegistrationMinimalMessage: Coding = {
  code: 'O44',
  display: 'Donor Registration - Minimal Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_DonorEligibilityObservationsMessage: Coding = {
  code: 'O45',
  display: 'Donor Eligibility Observations Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_DonorEligiblityMessage: Coding = {
  code: 'O46',
  display: 'Donor Eligiblity Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_DonorRequestToCollectMessage: Coding = {
  code: 'O47',
  display: 'Donor Request to Collect Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_DonationProcedureMessage: Coding = {
  code: 'O48',
  display: 'Donation Procedure Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PharmacyTreatmentDispenseRequestMessage: Coding = {
  code: 'O49',
  display: 'Pharmacy/Treatment Dispense Request Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PharmacyTreatmentEncodedOrderAcknowledgment: Coding = {
  code: 'O50',
  display: 'Pharmacy/Treatment Encoded Order Acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OSUOrderStatusUpdate: Coding = {
  code: 'O51',
  display: 'OSU – Order Status Update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OSUOrderStatusUpdateAcknowledgement: Coding = {
  code: 'O52',
  display: 'OSU – Order Status Update Acknowledgement',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORLGeneralLaboratoryOrderAcknowledgmentMessagePatientOptional: Coding = {
  code: 'O53',
  display: 'ORL - General Laboratory Order Acknowledgment Message (Patient Optional)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORLLaboratoryOrderAcknowledgmentMessageMultipleOrderPerSpecimenPatientOptional: Coding = {
  code: 'O54',
  display: 'ORL - Laboratory Order Acknowledgment Message  – Multiple Order Per Specimen (Patient Optional)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORLLaboratoryOrderAcknowledgmentMessageMultipleOrderPerContainerOfSpecimenPatientOptional: Coding = {
  code: 'O55',
  display: 'ORL - Laboratory Order Acknowledgment Message  – Multiple Order Per Container of Specimen (Patient Optional)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORLSpecimenShipmentCentricLaboratoryOrderAcknowledgmentMessagePatientOptional: Coding = {
  code: 'O56',
  display: 'ORL - Specimen Shipment Centric Laboratory Order Acknowledgment Message (Patient Optional)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OMQGeneralOrderMessageWithDocumentPayload: Coding = {
  code: 'O57',
  display: 'OMQ- General Order Message with Document Payload',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORXGeneralOrderMessageWithDocumentPayloadAcknowledgementMessage: Coding = {
  code: 'O58',
  display: 'ORX - General Order Message with Document Payload Acknowledgement Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OMLLaboratoryOrderForAdditionalWorkUp: Coding = {
  code: 'O59',
  display: 'OML - Laboratory order for additional work up',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_BARACKAddPatientAccounts: Coding = {
  code: 'P01',
  display: 'BAR/ACK - Add patient accounts',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_BARACKPurgePatientAccounts: Coding = {
  code: 'P02',
  display: 'BAR/ACK - Purge patient accounts',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_DFTACKPostDetailFinancialTransaction: Coding = {
  code: 'P03',
  display: 'DFT/ACK - Post detail financial transaction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_BARACKUpdateAccount: Coding = {
  code: 'P05',
  display: 'BAR/ACK - Update account',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_BARACKEndAccount: Coding = {
  code: 'P06',
  display: 'BAR/ACK - End account',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PEXUnsolicitedInitialIndividualProductExperienceReport: Coding = {
  code: 'P07',
  display: 'PEX - Unsolicited initial individual product experience report',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PEXUnsolicitedUpdateIndividualProductExperienceReport: Coding = {
  code: 'P08',
  display: 'PEX - Unsolicited update individual product experience report',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_BARACKTransmitAmbulatoryPaymentClassificationAPC: Coding = {
  code: 'P10',
  display: 'BAR/ACK -Transmit Ambulatory Payment  Classification(APC)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_DFTACKPostDetailFinancialTransactionsNew: Coding = {
  code: 'P11',
  display: 'DFT/ACK - Post Detail Financial Transactions - New',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_BARACKUpdateDiagnosisProcedure: Coding = {
  code: 'P12',
  display: 'BAR/ACK - Update Diagnosis/Procedure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PPRPCProblemAdd: Coding = {
  code: 'PC1',
  display: 'PPR - PC/ problem add',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PPRPCProblemUpdate: Coding = {
  code: 'PC2',
  display: 'PPR - PC/ problem update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PPRPCProblemDelete: Coding = {
  code: 'PC3',
  display: 'PPR - PC/ problem delete',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PGLPCGoalAdd: Coding = {
  code: 'PC6',
  display: 'PGL - PC/ goal add',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PGLPCGoalUpdate: Coding = {
  code: 'PC7',
  display: 'PGL - PC/ goal update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PGLPCGoalDelete: Coding = {
  code: 'PC8',
  display: 'PGL - PC/ goal delete',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PPPPCPathwayProblemOrientedAdd: Coding = {
  code: 'PCB',
  display: 'PPP - PC/ pathway (problem-oriented) add',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PPPPCPathwayProblemOrientedUpdate: Coding = {
  code: 'PCC',
  display: 'PPP - PC/ pathway (problem-oriented) update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PPPPCPathwayProblemOrientedDelete: Coding = {
  code: 'PCD',
  display: 'PPP - PC/ pathway (problem-oriented) delete',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PPGPCPathwayGoalOrientedAdd: Coding = {
  code: 'PCG',
  display: 'PPG - PC/ pathway (goal-oriented) add',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PPGPCPathwayGoalOrientedUpdate: Coding = {
  code: 'PCH',
  display: 'PPG - PC/ pathway (goal-oriented) update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_PPGPCPathwayGoalOrientedDelete: Coding = {
  code: 'PCJ',
  display: 'PPG - PC/ pathway (goal-oriented) delete',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_UDMACKUnsolicitedDisplayUpdateMessage: Coding = {
  code: 'Q05',
  display: 'UDM/ACK - Unsolicited display update message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QBPQueryByParameterRequestingAnRSPSegmentPatternResponse: Coding = {
  code: 'Q11',
  display: 'QBP - Query by parameter requesting an RSP segment pattern response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QBPQueryByParameterRequestingAnRTBTabularResponse: Coding = {
  code: 'Q13',
  display: 'QBP - Query by parameter requesting an  RTB - tabular response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QBPQueryByParameterRequestingAnRDYDisplayResponse: Coding = {
  code: 'Q15',
  display: 'QBP - Query by parameter requesting an RDY display response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QSBCreateSubscription: Coding = {
  code: 'Q16',
  display: 'QSB - Create subscription',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QVRQueryForPreviousEvents: Coding = {
  code: 'Q17',
  display: 'QVR - Query for previous events',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QBPGetPersonDemographics: Coding = {
  code: 'Q21',
  display: 'QBP - Get person demographics',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QBPFindCandidates: Coding = {
  code: 'Q22',
  display: 'QBP - Find candidates',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QBPGetCorrespondingIdentifiers: Coding = {
  code: 'Q23',
  display: 'QBP - Get corresponding identifiers',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QBPAllocateIdentifiers: Coding = {
  code: 'Q24',
  display: 'QBP - Allocate identifiers',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QBPPersonnelInformationBySegmentQuery: Coding = {
  code: 'Q25',
  display: 'QBP - Personnel Information by Segment Query',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QBPQueryDispenseHistory: Coding = {
  code: 'Q31',
  display: 'QBP Query Dispense history',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_FindCandidatesIncludingVisitInformation: Coding = {
  code: 'Q32',
  display: 'Find Candidates including Visit Information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QBPGetDonorRecordCandidates: Coding = {
  code: 'Q33',
  display: 'QBP - Get Donor Record Candidates',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_QBPGetDonorRecord: Coding = {
  code: 'Q34',
  display: 'QBP - Get Donor Record',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORUACKUnsolicitedTransmissionOfAnObservationMessage: Coding = {
  code: 'R01',
  display: 'ORU/ACK - Unsolicited transmission of an observation message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OULUnsolicitedSpecimenOrientedObservationMessage: Coding = {
  code: 'R22',
  display: 'OUL - Unsolicited Specimen Oriented Observation Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OULUnsolicitedSpecimenContainerOrientedObservationMessage: Coding = {
  code: 'R23',
  display: 'OUL - Unsolicited Specimen Container Oriented Observation Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OULUnsolicitedOrderOrientedObservationMessage: Coding = {
  code: 'R24',
  display: 'OUL - Unsolicited Order Oriented Observation Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OPUUnsolicitedPopulationLocationBasedLaboratoryObservationMessage: Coding = {
  code: 'R25',
  display: 'OPU - Unsolicited Population/Location-Based Laboratory Observation Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_OSMUnsolicitedSpecimenShipmentManifestMessage: Coding = {
  code: 'R26',
  display: 'OSM - Unsolicited Specimen Shipment Manifest Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORUUnsolicitedPointOfCareObservationMessageWithoutExistingOrderPlaceAnOrder: Coding = {
  code: 'R30',
  display: 'ORU - Unsolicited Point-Of-Care Observation Message Without Existing Order - Place An Order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORUUnsolicitedNewPointOfCareObservationMessageSearchForAnOrder: Coding = {
  code: 'R31',
  display: 'ORU - Unsolicited New Point-Of-Care Observation Message - Search For An Order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORUUnsolicitedPreOrderedPointOfCareObservation: Coding = {
  code: 'R32',
  display: 'ORU - Unsolicited Pre-Ordered Point-Of-Care Observation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORAObservationReportAcknowledgement: Coding = {
  code: 'R33',
  display: 'ORA - Observation Report Acknowledgement',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORUUnsolicitedReportAlarm: Coding = {
  code: 'R40',
  display: 'ORU - Unsolicited Report Alarm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ObservationReportAlertAcknowledgement: Coding = {
  code: 'R41',
  display: 'Observation Report Alert Acknowledgement',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORUUnsolicitedDeviceEventObservationMessage: Coding = {
  code: 'R42',
  display: 'ORU – Unsolicited Device Event Observation Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ORUUnsolicitedPatientDeviceAssociationObservationMessage: Coding = {
  code: 'R43',
  display: 'ORU – Unsolicited Patient-Device Association Observation Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SRMSRRRequestNewAppointmentBooking: Coding = {
  code: 'S01',
  display: 'SRM/SRR - Request new appointment booking',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SRMSRRRequestAppointmentRescheduling: Coding = {
  code: 'S02',
  display: 'SRM/SRR - Request appointment rescheduling',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SRMSRRRequestAppointmentModification: Coding = {
  code: 'S03',
  display: 'SRM/SRR - Request appointment modification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SRMSRRRequestAppointmentCancellation: Coding = {
  code: 'S04',
  display: 'SRM/SRR - Request appointment cancellation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SRMSRRRequestAppointmentDiscontinuation: Coding = {
  code: 'S05',
  display: 'SRM/SRR - Request appointment discontinuation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SRMSRRRequestAppointmentDeletion: Coding = {
  code: 'S06',
  display: 'SRM/SRR - Request appointment deletion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SRMSRRRequestAdditionOfServiceResourceOnAppointment: Coding = {
  code: 'S07',
  display: 'SRM/SRR - Request addition of service/resource on appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SRMSRRRequestModificationOfServiceResourceOnAppointment: Coding = {
  code: 'S08',
  display: 'SRM/SRR - Request modification of service/resource on appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SRMSRRRequestCancellationOfServiceResourceOnAppointment: Coding = {
  code: 'S09',
  display: 'SRM/SRR - Request cancellation of service/resource on appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SRMSRRRequestDiscontinuationOfServiceResourceOnAppointment: Coding = {
  code: 'S10',
  display: 'SRM/SRR - Request discontinuation of service/resource on appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SRMSRRRequestDeletionOfServiceResourceOnAppointment: Coding = {
  code: 'S11',
  display: 'SRM/SRR - Request deletion of service/resource on appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationOfNewAppointmentBooking: Coding = {
  code: 'S12',
  display: 'SIU/ACK - Notification of new appointment booking',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationOfAppointmentRescheduling: Coding = {
  code: 'S13',
  display: 'SIU/ACK - Notification of appointment rescheduling',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationOfAppointmentModification: Coding = {
  code: 'S14',
  display: 'SIU/ACK - Notification of appointment modification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationOfAppointmentCancellation: Coding = {
  code: 'S15',
  display: 'SIU/ACK - Notification of appointment cancellation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationOfAppointmentDiscontinuation: Coding = {
  code: 'S16',
  display: 'SIU/ACK - Notification of appointment discontinuation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationOfAppointmentDeletion: Coding = {
  code: 'S17',
  display: 'SIU/ACK - Notification of appointment deletion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationOfAdditionOfServiceResourceOnAppointment: Coding = {
  code: 'S18',
  display: 'SIU/ACK - Notification of addition of service/resource on appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationOfModificationOfServiceResourceOnAppointment: Coding = {
  code: 'S19',
  display: 'SIU/ACK - Notification of modification of service/resource on appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationOfCancellationOfServiceResourceOnAppointment: Coding = {
  code: 'S20',
  display: 'SIU/ACK - Notification of cancellation of service/resource on appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationOfDiscontinuationOfServiceResourceOnAppointment: Coding = {
  code: 'S21',
  display: 'SIU/ACK - Notification of discontinuation of service/resource on appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationOfDeletionOfServiceResourceOnAppointment: Coding = {
  code: 'S22',
  display: 'SIU/ACK - Notification of deletion of service/resource on appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationOfBlockedScheduleTimeSlotS: Coding = {
  code: 'S23',
  display: 'SIU/ACK - Notification of blocked schedule time slot(s)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationOfOpenedQuotationUnblockedQuotationScheduleTimeSlotS: Coding = {
  code: 'S24',
  display: 'SIU/ACK - Notification of opened ("unblocked") schedule time slot(s)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKNotificationThatPatientDidNotShowUpForScheduleAppointment: Coding = {
  code: 'S26',
  display: 'SIU/ACK Notification that patient did not show up for schedule appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SIUACKBroadcastNotificationOfScheduledAppointments: Coding = {
  code: 'S27',
  display: 'SIU/ACK - Broadcast Notification of Scheduled Appointments',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SLRSLSRequestNewSterilizationLot: Coding = {
  code: 'S28',
  display: 'SLR/SLS - Request new sterilization lot',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SLRSLSRequestSterilizationLotDeletion: Coding = {
  code: 'S29',
  display: 'SLR/SLS - Request Sterilization lot deletion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_STISTSRequestItem: Coding = {
  code: 'S30',
  display: 'STI/STS - Request item',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SDRSDSRequestAntiMicrobialDeviceData: Coding = {
  code: 'S31',
  display: 'SDR/SDS - Request anti-microbial device data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SMDSMSRequestAntiMicrobialDeviceCycleData: Coding = {
  code: 'S32',
  display: 'SMD/SMS - Request anti-microbial device cycle data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_STCACKNotificationOfSterilizationConfiguration: Coding = {
  code: 'S33',
  display: 'STC/ACK - Notification of sterilization configuration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SLNACKNotificationOfSterilizationLot: Coding = {
  code: 'S34',
  display: 'SLN/ACK - Notification of sterilization lot',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SLNACKNotificationOfSterilizationLotDeletion: Coding = {
  code: 'S35',
  display: 'SLN/ACK - Notification of sterilization lot deletion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SDNACKNotificationOfAntiMicrobialDeviceData: Coding = {
  code: 'S36',
  display: 'SDN/ACK - Notification of anti-microbial device data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SCNACKNotificationOfAntiMicrobialDeviceCycleData: Coding = {
  code: 'S37',
  display: 'SCN/ACK - Notification of anti-microbial device cycle data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MDMACKOriginalDocumentNotification: Coding = {
  code: 'T01',
  display: 'MDM/ACK - Original document notification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MDMACKOriginalDocumentNotificationAndContent: Coding = {
  code: 'T02',
  display: 'MDM/ACK - Original document notification and content',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MDMACKDocumentStatusChangeNotification: Coding = {
  code: 'T03',
  display: 'MDM/ACK - Document status change notification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MDMACKDocumentStatusChangeNotificationAndContent: Coding = {
  code: 'T04',
  display: 'MDM/ACK - Document status change notification and content',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MDMACKDocumentAddendumNotification: Coding = {
  code: 'T05',
  display: 'MDM/ACK - Document addendum notification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MDMACKDocumentAddendumNotificationAndContent: Coding = {
  code: 'T06',
  display: 'MDM/ACK - Document addendum notification and content',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MDMACKDocumentEditNotification: Coding = {
  code: 'T07',
  display: 'MDM/ACK - Document edit notification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MDMACKDocumentEditNotificationAndContent: Coding = {
  code: 'T08',
  display: 'MDM/ACK - Document edit notification and content',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MDMACKDocumentReplacementNotification: Coding = {
  code: 'T09',
  display: 'MDM/ACK - Document replacement notification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MDMACKDocumentReplacementNotificationAndContent: Coding = {
  code: 'T10',
  display: 'MDM/ACK - Document replacement notification and content',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MDMACKDocumentCancelNotification: Coding = {
  code: 'T11',
  display: 'MDM/ACK - Document cancel notification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ESUACKAutomatedEquipmentStatusUpdate: Coding = {
  code: 'U01',
  display: 'ESU/ACK - Automated equipment status update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_ESRACKAutomatedEquipmentStatusRequest: Coding = {
  code: 'U02',
  display: 'ESR/ACK - Automated equipment status request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SSUACKSpecimenStatusUpdate: Coding = {
  code: 'U03',
  display: 'SSU/ACK - Specimen status update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_SSRACKSpecimenStatusRequest: Coding = {
  code: 'U04',
  display: 'SSR/ACK - specimen status request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_INUACKAutomatedEquipmentInventoryUpdate: Coding = {
  code: 'U05',
  display: 'INU/ACK  - Automated equipment inventory update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_INRACKAutomatedEquipmentInventoryRequest: Coding = {
  code: 'U06',
  display: 'INR/ACK - Automated equipment inventory request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_EACACKAutomatedEquipmentCommand: Coding = {
  code: 'U07',
  display: 'EAC/ACK - Automated equipment command',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_EARACKAutomatedEquipmentResponse: Coding = {
  code: 'U08',
  display: 'EAR/ACK - Automated equipment response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_EANACKAutomatedEquipmentNotification: Coding = {
  code: 'U09',
  display: 'EAN/ACK - Automated equipment notification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_TCUACKAutomatedEquipmentTestCodeSettingsUpdate: Coding = {
  code: 'U10',
  display: 'TCU/ACK - Automated equipment test code settings update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_TCRACKAutomatedEquipmentTestCodeSettingsRequest: Coding = {
  code: 'U11',
  display: 'TCR/ACK - Automated equipment test code settings request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_LSUACKAutomatedEquipmentLogServiceUpdate: Coding = {
  code: 'U12',
  display: 'LSU/ACK - Automated equipment log/service update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_LSRACKAutomatedEquipmentLogServiceRequest: Coding = {
  code: 'U13',
  display: 'LSR/ACK - Automated equipment log/service request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_INRACKAutomatedEquipmentInventoryRequest_2: Coding = {
  code: 'U14',
  display: 'INR/ACK – Automated Equipment Inventory Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_VXUUnsolicitedVaccinationRecordUpdate: Coding = {
  code: 'V04',
  display: 'VXU - Unsolicited vaccination record update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
const v2_0003_MFQMFRMasterFilesQueryUseEventSameAsAskingForEGM05Location: Coding = {
  code: 'Varies',
  display: 'MFQ/MFR - Master files query (use event same as asking for e.g., M05 - location)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0003',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0003 ( EVENT TYPE CODE)
 */
export const V20003 = {
  ADTACKAdmitVisitNotification: v2_0003_ADTACKAdmitVisitNotification,
  ADTACKTransferAPatient: v2_0003_ADTACKTransferAPatient,
  ADTACKDischargeEndVisit: v2_0003_ADTACKDischargeEndVisit,
  ADTACKRegisterAPatient: v2_0003_ADTACKRegisterAPatient,
  ADTACKPreAdmitAPatient: v2_0003_ADTACKPreAdmitAPatient,
  ADTACKChangeAnOutpatientToAnInpatient: v2_0003_ADTACKChangeAnOutpatientToAnInpatient,
  ADTACKChangeAnInpatientToAnOutpatient: v2_0003_ADTACKChangeAnInpatientToAnOutpatient,
  ADTACKUpdatePatientInformation: v2_0003_ADTACKUpdatePatientInformation,
  ADTACKPatientDepartingTracking: v2_0003_ADTACKPatientDepartingTracking,
  ADTACKPatientArrivingTracking: v2_0003_ADTACKPatientArrivingTracking,
  ADTACKCancelAdmitVisitNotification: v2_0003_ADTACKCancelAdmitVisitNotification,
  ADTACKCancelTransfer: v2_0003_ADTACKCancelTransfer,
  ADTACKCancelDischargeEndVisit: v2_0003_ADTACKCancelDischargeEndVisit,
  ADTACKPendingAdmit: v2_0003_ADTACKPendingAdmit,
  ADTACKPendingTransfer: v2_0003_ADTACKPendingTransfer,
  ADTACKPendingDischarge: v2_0003_ADTACKPendingDischarge,
  ADTACKSwapPatients: v2_0003_ADTACKSwapPatients,
  ADTACKBedStatusUpdate: v2_0003_ADTACKBedStatusUpdate,
  ADTACKPatientGoesOnAQuotationLeaveOfAbsenceQuotation: v2_0003_ADTACKPatientGoesOnAQuotationLeaveOfAbsenceQuotation,
  ADTACKPatientReturnsFromAQuotationLeaveOfAbsenceQuotation: v2_0003_ADTACKPatientReturnsFromAQuotationLeaveOfAbsenceQuotation,
  ADTACKDeleteAPatientRecord: v2_0003_ADTACKDeleteAPatientRecord,
  ADTACKLinkPatientInformation: v2_0003_ADTACKLinkPatientInformation,
  ADTACKCancelPendingDischarge: v2_0003_ADTACKCancelPendingDischarge,
  ADTACKCancelPendingTransfer: v2_0003_ADTACKCancelPendingTransfer,
  ADTACKCancelPendingAdmit: v2_0003_ADTACKCancelPendingAdmit,
  ADTACKAddPersonInformation: v2_0003_ADTACKAddPersonInformation,
  ADTACKDeletePersonInformation: v2_0003_ADTACKDeletePersonInformation,
  ADTACKUpdatePersonInformation: v2_0003_ADTACKUpdatePersonInformation,
  ADTACKCancelPatientArrivingTracking: v2_0003_ADTACKCancelPatientArrivingTracking,
  ADTACKCancelPatientDepartingTracking: v2_0003_ADTACKCancelPatientDepartingTracking,
  ADTACKUnlinkPatientInformation: v2_0003_ADTACKUnlinkPatientInformation,
  ADTACKCancelPreAdmit: v2_0003_ADTACKCancelPreAdmit,
  ADTACKMergePatientPatientIdentifierList: v2_0003_ADTACKMergePatientPatientIdentifierList,
  ADTACKMergeAccountPatientAccountNumber: v2_0003_ADTACKMergeAccountPatientAccountNumber,
  ADTACKMergeVisitVisitNumber: v2_0003_ADTACKMergeVisitVisitNumber,
  ADTACKMovePatientInformationPatientIdentifierList: v2_0003_ADTACKMovePatientInformationPatientIdentifierList,
  ADTACKMoveAccountInformationPatientAccountNumber: v2_0003_ADTACKMoveAccountInformationPatientAccountNumber,
  ADTACKMoveVisitInformationVisitNumber: v2_0003_ADTACKMoveVisitInformationVisitNumber,
  ADTACKChangePatientIdentifierList: v2_0003_ADTACKChangePatientIdentifierList,
  ADTACKChangePatientAccountNumber: v2_0003_ADTACKChangePatientAccountNumber,
  ADTACKChangeVisitNumber: v2_0003_ADTACKChangeVisitNumber,
  ADTACKChangeAlternateVisitID: v2_0003_ADTACKChangeAlternateVisitID,
  ADTACKCancelLeaveOfAbsenceForAPatient: v2_0003_ADTACKCancelLeaveOfAbsenceForAPatient,
  ADTACKCancelPatientReturnsFromALeaveOfAbsence: v2_0003_ADTACKCancelPatientReturnsFromALeaveOfAbsence,
  ADTACKChangeAttendingDoctor: v2_0003_ADTACKChangeAttendingDoctor,
  ADTACKCancelChangeAttendingDoctor: v2_0003_ADTACKCancelChangeAttendingDoctor,
  ADTACKUpdateAllergyInformation: v2_0003_ADTACKUpdateAllergyInformation,
  ADTACKChangeConsultingDoctor: v2_0003_ADTACKChangeConsultingDoctor,
  ADTACKCancelChangeConsultingDoctor: v2_0003_ADTACKCancelChangeConsultingDoctor,
  PMUACKAddPersonnelRecord: v2_0003_PMUACKAddPersonnelRecord,
  PMUACKUpdatePersonnelRecord: v2_0003_PMUACKUpdatePersonnelRecord,
  PMUACKDeletePersonnelReCord: v2_0003_PMUACKDeletePersonnelReCord,
  PMUACKActivePracticingPerson: v2_0003_PMUACKActivePracticingPerson,
  PMUACKDeactivatePracticingPerson: v2_0003_PMUACKDeactivatePracticingPerson,
  PMUACKTerminatePracticingPerson: v2_0003_PMUACKTerminatePracticingPerson,
  PMUACKGrantCertificatePermission: v2_0003_PMUACKGrantCertificatePermission,
  PMUACKRevokeCertificatePermission: v2_0003_PMUACKRevokeCertificatePermission,
  CRMRegisterAPatientOnAClinicalTrial: v2_0003_CRMRegisterAPatientOnAClinicalTrial,
  CRMCancelAPatientRegistrationOnClinicalTrialForClericalMistakesOnly: v2_0003_CRMCancelAPatientRegistrationOnClinicalTrialForClericalMistakesOnly,
  CRMCorrectUpdateRegistrationInformation: v2_0003_CRMCorrectUpdateRegistrationInformation,
  CRMPatientHasGoneOffAClinicalTrial: v2_0003_CRMPatientHasGoneOffAClinicalTrial,
  CRMPatientEntersPhaseOfClinicalTrial: v2_0003_CRMPatientEntersPhaseOfClinicalTrial,
  CRMCancelPatientEnteringAPhaseClericalMistake: v2_0003_CRMCancelPatientEnteringAPhaseClericalMistake,
  CRMCorrectUpdatePhaseInformation: v2_0003_CRMCorrectUpdatePhaseInformation,
  CRMPatientHasGoneOffPhaseOfClinicalTrial: v2_0003_CRMPatientHasGoneOffPhaseOfClinicalTrial,
  CSUAutomatedTimeIntervalsForReportingLikeMonthly: v2_0003_CSUAutomatedTimeIntervalsForReportingLikeMonthly,
  CSUPatientCompletesTheClinicalTrial: v2_0003_CSUPatientCompletesTheClinicalTrial,
  CSUPatientCompletesAPhaseOfTheClinicalTrial: v2_0003_CSUPatientCompletesAPhaseOfTheClinicalTrial,
  CSUUpdateCorrectionOfPatientOrderResultInformation: v2_0003_CSUUpdateCorrectionOfPatientOrderResultInformation,
  CancelQuery: v2_0003_CancelQuery,
  SubmitHealthCareServicesInvoice: v2_0003_SubmitHealthCareServicesInvoice,
  CancelHealthCareServicesInvoice: v2_0003_CancelHealthCareServicesInvoice,
  HealthCareServicesInvoiceStatus: v2_0003_HealthCareServicesInvoiceStatus,
  ReAssessHealthCareServicesInvoiceRequest: v2_0003_ReAssessHealthCareServicesInvoiceRequest,
  EditAdjudicationResults: v2_0003_EditAdjudicationResults,
  RequestAdditionalInformation: v2_0003_RequestAdditionalInformation,
  AdditionalInformationResponse: v2_0003_AdditionalInformationResponse,
  PaymentRemittanceAdvice: v2_0003_PaymentRemittanceAdvice,
  SubmitAuthorizationRequest: v2_0003_SubmitAuthorizationRequest,
  CancelAuthorizationRequest: v2_0003_CancelAuthorizationRequest,
  AuthorizationRequestStatus: v2_0003_AuthorizationRequestStatus,
  AuthorizationResponse: v2_0003_AuthorizationResponse,
  SubmitHealthDocumentRelatedToAuthorizationRequest: v2_0003_SubmitHealthDocumentRelatedToAuthorizationRequest,
  CancelHealthDocumentRelatedToAuthorizationRequest: v2_0003_CancelHealthDocumentRelatedToAuthorizationRequest,
  RQIRPIRequestForInsuranceInformation: v2_0003_RQIRPIRequestForInsuranceInformation,
  RQIRPLRequestReceiptOfPatientSelectionDisplayList: v2_0003_RQIRPLRequestReceiptOfPatientSelectionDisplayList,
  RQIRPRRequestReceiptOfPatientSelectionList: v2_0003_RQIRPRRequestReceiptOfPatientSelectionList,
  RQDRPIRequestForPatientDemographicData: v2_0003_RQDRPIRequestForPatientDemographicData,
  PINACKUnsolicitedInsuranceInformation: v2_0003_PINACKUnsolicitedInsuranceInformation,
  RQARPARequestForTreatmentAuthorizationInformation: v2_0003_RQARPARequestForTreatmentAuthorizationInformation,
  RQARPARequestForModificationToAnAuthorization: v2_0003_RQARPARequestForModificationToAnAuthorization,
  RQARPARequestForResubmissionOfAnAuthorization: v2_0003_RQARPARequestForResubmissionOfAnAuthorization,
  RQARPARequestForCancellationOfAnAuthorization: v2_0003_RQARPARequestForCancellationOfAnAuthorization,
  REFRRIPatientReferral: v2_0003_REFRRIPatientReferral,
  REFRRIModifyPatientReferral: v2_0003_REFRRIModifyPatientReferral,
  REFRRICancelPatientReferral: v2_0003_REFRRICancelPatientReferral,
  REFRRIRequestPatientReferralStatus: v2_0003_REFRRIRequestPatientReferralStatus,
  CollaborativeCareReferral: v2_0003_CollaborativeCareReferral,
  CollaborativeCareQueryCollaborativeCareQueryUpdate: v2_0003_CollaborativeCareQueryCollaborativeCareQueryUpdate,
  AsynchronousCollaborativeCareUpdate: v2_0003_AsynchronousCollaborativeCareUpdate,
  CollaborativeCareMessage: v2_0003_CollaborativeCareMessage,
  CollaborativeCareFetchCollaborativeCareInformation: v2_0003_CollaborativeCareFetchCollaborativeCareInformation,
  QCNACKCancelQueryAcknowledgeMessage: v2_0003_QCNACKCancelQueryAcknowledgeMessage,
  QSXACKCancelSubscriptionAcknowledgeMessage: v2_0003_QSXACKCancelSubscriptionAcknowledgeMessage,
  RSPSegmentPatternResponseInResponseToQBPPowerQ11: v2_0003_RSPSegmentPatternResponseInResponseToQBPPowerQ11,
  RTBTabularResponseInResponseToQBPPowerQ13: v2_0003_RTBTabularResponseInResponseToQBPPowerQ13,
  RDYDisplayResponseInResponseToQBPPowerQ15: v2_0003_RDYDisplayResponseInResponseToQBPPowerQ15,
  RSPGetPersonDemographicsResponse: v2_0003_RSPGetPersonDemographicsResponse,
  RSPFindCandidatesResponse: v2_0003_RSPFindCandidatesResponse,
  RSPGetCorrespondingIdentifiersResponse: v2_0003_RSPGetCorrespondingIdentifiersResponse,
  RSPAllocateIdentifiersResponse: v2_0003_RSPAllocateIdentifiersResponse,
  RSPPersonnelInformationBySegmentResponse: v2_0003_RSPPersonnelInformationBySegmentResponse,
  RSPDispenseHistoryResponse: v2_0003_RSPDispenseHistoryResponse,
  FindCandidatesIncludingVisitInformationResponse: v2_0003_FindCandidatesIncludingVisitInformationResponse,
  GetDonorRecordCandidatesResponseMessage: v2_0003_GetDonorRecordCandidatesResponseMessage,
  SegmentPatternResponseMessage: v2_0003_SegmentPatternResponseMessage,
  MFNMFKMasterFileStaffPractitioner: v2_0003_MFNMFKMasterFileStaffPractitioner,
  MFNMFKMasterFilesChargeDescription: v2_0003_MFNMFKMasterFilesChargeDescription,
  MFNMFKPatientLocationMasterFile: v2_0003_MFNMFKPatientLocationMasterFile,
  MFNMFKClinicalStudyWithPhasesAndSchedulesMasterFile: v2_0003_MFNMFKClinicalStudyWithPhasesAndSchedulesMasterFile,
  MFNMFKClinicalStudyWithoutPhasesButWithSchedulesMasterFile: v2_0003_MFNMFKClinicalStudyWithoutPhasesButWithSchedulesMasterFile,
  MFNMFKTestObservationNumericMasterFile: v2_0003_MFNMFKTestObservationNumericMasterFile,
  MFNMFKTestObservationCategoricalMasterFile: v2_0003_MFNMFKTestObservationCategoricalMasterFile,
  MFNMFKTestObservationBatteriesMasterFile: v2_0003_MFNMFKTestObservationBatteriesMasterFile,
  MFNMFKTestCalculatedObservationsMasterFile: v2_0003_MFNMFKTestCalculatedObservationsMasterFile,
  MFNMFKMasterFileNotificationMessage: v2_0003_MFNMFKMasterFileNotificationMessage,
  MFNMFKMasterFileNotificationGeneral: v2_0003_MFNMFKMasterFileNotificationGeneral,
  MFNMFKMasterFileNotificationSiteDefined: v2_0003_MFNMFKMasterFileNotificationSiteDefined,
  MFNMFKInventoryItemMasterFileNotification: v2_0003_MFNMFKInventoryItemMasterFileNotification,
  MFNMFKMasterFileNotificationInventoryItemEnhanced: v2_0003_MFNMFKMasterFileNotificationInventoryItemEnhanced,
  DRGMasterFileMessage: v2_0003_DRGMasterFileMessage,
  MFNMFKMasterFileNotificationTestObservationPayer: v2_0003_MFNMFKMasterFileNotificationTestObservationPayer,
  NMDACKApplicationManagementDataMessageUnsolicited: v2_0003_NMDACKApplicationManagementDataMessageUnsolicited,
  OMDDietOrder: v2_0003_OMDDietOrder,
  ORDDietOrderAcknowledgment: v2_0003_ORDDietOrderAcknowledgment,
  OMSStockRequisitionOrder: v2_0003_OMSStockRequisitionOrder,
  ORSStockRequisitionAcknowledgment: v2_0003_ORSStockRequisitionAcknowledgment,
  OMNNonStockRequisitionOrder: v2_0003_OMNNonStockRequisitionOrder,
  ORNNonStockRequisitionAcknowledgment: v2_0003_ORNNonStockRequisitionAcknowledgment,
  OMPPharmacyTreatmentOrder: v2_0003_OMPPharmacyTreatmentOrder,
  ORPPharmacyTreatmentOrderAcknowledgment: v2_0003_ORPPharmacyTreatmentOrderAcknowledgment,
  RDEPharmacyTreatmentEncodedOrder: v2_0003_RDEPharmacyTreatmentEncodedOrder,
  RREPharmacyTreatmentEncodedOrderAcknowledgment: v2_0003_RREPharmacyTreatmentEncodedOrderAcknowledgment,
  RDSPharmacyTreatmentDispense: v2_0003_RDSPharmacyTreatmentDispense,
  RRDPharmacyTreatmentDispenseAcknowledgment: v2_0003_RRDPharmacyTreatmentDispenseAcknowledgment,
  RGVPharmacyTreatmentGive: v2_0003_RGVPharmacyTreatmentGive,
  RRGPharmacyTreatmentGiveAcknowledgment: v2_0003_RRGPharmacyTreatmentGiveAcknowledgment,
  RASPharmacyTreatmentAdministration: v2_0003_RASPharmacyTreatmentAdministration,
  RRAPharmacyTreatmentAdministrationAcknowledgment: v2_0003_RRAPharmacyTreatmentAdministrationAcknowledgment,
  OMGGeneralClinicalOrder: v2_0003_OMGGeneralClinicalOrder,
  ORGORLGeneralClinicalOrderResponse: v2_0003_ORGORLGeneralClinicalOrderResponse,
  OMLLaboratoryOrder: v2_0003_OMLLaboratoryOrder,
  ORLGeneralLaboratoryOrderResponseMessageToAnyOML: v2_0003_ORLGeneralLaboratoryOrderResponseMessageToAnyOML,
  OMIImagingOrder: v2_0003_OMIImagingOrder,
  ORIImagingOrderResponseMessageToAnyOMI: v2_0003_ORIImagingOrderResponseMessageToAnyOMI,
  RDEPharmacyTreatmentRefillAuthorizationRequest: v2_0003_RDEPharmacyTreatmentRefillAuthorizationRequest,
  RREPharmacyTreatmentRefillAuthorizationAcknowledgement: v2_0003_RREPharmacyTreatmentRefillAuthorizationAcknowledgement,
  OMBBloodProductOrder: v2_0003_OMBBloodProductOrder,
  ORBBloodProductOrderAcknowledgment: v2_0003_ORBBloodProductOrderAcknowledgment,
  BPSBloodProductDispenseStatus: v2_0003_BPSBloodProductDispenseStatus,
  BRPBloodProductDispenseStatusAcknowledgment: v2_0003_BRPBloodProductDispenseStatusAcknowledgment,
  BTSBloodProductTransfusionDisposition: v2_0003_BTSBloodProductTransfusionDisposition,
  BRTBloodProductTransfusionDispositionAcknowledgment: v2_0003_BRTBloodProductTransfusionDispositionAcknowledgment,
  OMLLaboratoryOrderForMultipleOrdersRelatedToASingleSpecimen: v2_0003_OMLLaboratoryOrderForMultipleOrdersRelatedToASingleSpecimen,
  ORLLaboratoryOrderResponseMessageToAMultipleOrderRelatedToSingleSpecimenOML: v2_0003_ORLLaboratoryOrderResponseMessageToAMultipleOrderRelatedToSingleSpecimenOML,
  OMLLaboratoryOrderForMultipleOrdersRelatedToASingleContainerOfASpecimen: v2_0003_OMLLaboratoryOrderForMultipleOrdersRelatedToASingleContainerOfASpecimen,
  ORLLaboratoryOrderResponseMessageToASingleContainerOfASpecimenOML: v2_0003_ORLLaboratoryOrderResponseMessageToASingleContainerOfASpecimenOML,
  OPLPopulationLocationBasedLaboratoryOrderMessage: v2_0003_OPLPopulationLocationBasedLaboratoryOrderMessage,
  OPRPopulationLocationBasedLaboratoryOrderAcknowledgmentMessage: v2_0003_OPRPopulationLocationBasedLaboratoryOrderAcknowledgmentMessage,
  SpecimenShipmentCentricLaboratoryOrder: v2_0003_SpecimenShipmentCentricLaboratoryOrder,
  SpecimenShipmentCentricLaboratoryOrderAcknowledgmentMessage: v2_0003_SpecimenShipmentCentricLaboratoryOrderAcknowledgmentMessage,
  DBCCreateDonorRecordMessage: v2_0003_DBCCreateDonorRecordMessage,
  DBUUpdateDonorRecordMessage: v2_0003_DBUUpdateDonorRecordMessage,
  GeneralOrderMessageWithDocumentPayloadAcknowledgementMessage: v2_0003_GeneralOrderMessageWithDocumentPayloadAcknowledgementMessage,
  DonorRegistrationMinimalMessage: v2_0003_DonorRegistrationMinimalMessage,
  DonorEligibilityObservationsMessage: v2_0003_DonorEligibilityObservationsMessage,
  DonorEligiblityMessage: v2_0003_DonorEligiblityMessage,
  DonorRequestToCollectMessage: v2_0003_DonorRequestToCollectMessage,
  DonationProcedureMessage: v2_0003_DonationProcedureMessage,
  PharmacyTreatmentDispenseRequestMessage: v2_0003_PharmacyTreatmentDispenseRequestMessage,
  PharmacyTreatmentEncodedOrderAcknowledgment: v2_0003_PharmacyTreatmentEncodedOrderAcknowledgment,
  OSUOrderStatusUpdate: v2_0003_OSUOrderStatusUpdate,
  OSUOrderStatusUpdateAcknowledgement: v2_0003_OSUOrderStatusUpdateAcknowledgement,
  ORLGeneralLaboratoryOrderAcknowledgmentMessagePatientOptional: v2_0003_ORLGeneralLaboratoryOrderAcknowledgmentMessagePatientOptional,
  ORLLaboratoryOrderAcknowledgmentMessageMultipleOrderPerSpecimenPatientOptional: v2_0003_ORLLaboratoryOrderAcknowledgmentMessageMultipleOrderPerSpecimenPatientOptional,
  ORLLaboratoryOrderAcknowledgmentMessageMultipleOrderPerContainerOfSpecimenPatientOptional: v2_0003_ORLLaboratoryOrderAcknowledgmentMessageMultipleOrderPerContainerOfSpecimenPatientOptional,
  ORLSpecimenShipmentCentricLaboratoryOrderAcknowledgmentMessagePatientOptional: v2_0003_ORLSpecimenShipmentCentricLaboratoryOrderAcknowledgmentMessagePatientOptional,
  OMQGeneralOrderMessageWithDocumentPayload: v2_0003_OMQGeneralOrderMessageWithDocumentPayload,
  ORXGeneralOrderMessageWithDocumentPayloadAcknowledgementMessage: v2_0003_ORXGeneralOrderMessageWithDocumentPayloadAcknowledgementMessage,
  OMLLaboratoryOrderForAdditionalWorkUp: v2_0003_OMLLaboratoryOrderForAdditionalWorkUp,
  BARACKAddPatientAccounts: v2_0003_BARACKAddPatientAccounts,
  BARACKPurgePatientAccounts: v2_0003_BARACKPurgePatientAccounts,
  DFTACKPostDetailFinancialTransaction: v2_0003_DFTACKPostDetailFinancialTransaction,
  BARACKUpdateAccount: v2_0003_BARACKUpdateAccount,
  BARACKEndAccount: v2_0003_BARACKEndAccount,
  PEXUnsolicitedInitialIndividualProductExperienceReport: v2_0003_PEXUnsolicitedInitialIndividualProductExperienceReport,
  PEXUnsolicitedUpdateIndividualProductExperienceReport: v2_0003_PEXUnsolicitedUpdateIndividualProductExperienceReport,
  BARACKTransmitAmbulatoryPaymentClassificationAPC: v2_0003_BARACKTransmitAmbulatoryPaymentClassificationAPC,
  DFTACKPostDetailFinancialTransactionsNew: v2_0003_DFTACKPostDetailFinancialTransactionsNew,
  BARACKUpdateDiagnosisProcedure: v2_0003_BARACKUpdateDiagnosisProcedure,
  PPRPCProblemAdd: v2_0003_PPRPCProblemAdd,
  PPRPCProblemUpdate: v2_0003_PPRPCProblemUpdate,
  PPRPCProblemDelete: v2_0003_PPRPCProblemDelete,
  PGLPCGoalAdd: v2_0003_PGLPCGoalAdd,
  PGLPCGoalUpdate: v2_0003_PGLPCGoalUpdate,
  PGLPCGoalDelete: v2_0003_PGLPCGoalDelete,
  PPPPCPathwayProblemOrientedAdd: v2_0003_PPPPCPathwayProblemOrientedAdd,
  PPPPCPathwayProblemOrientedUpdate: v2_0003_PPPPCPathwayProblemOrientedUpdate,
  PPPPCPathwayProblemOrientedDelete: v2_0003_PPPPCPathwayProblemOrientedDelete,
  PPGPCPathwayGoalOrientedAdd: v2_0003_PPGPCPathwayGoalOrientedAdd,
  PPGPCPathwayGoalOrientedUpdate: v2_0003_PPGPCPathwayGoalOrientedUpdate,
  PPGPCPathwayGoalOrientedDelete: v2_0003_PPGPCPathwayGoalOrientedDelete,
  UDMACKUnsolicitedDisplayUpdateMessage: v2_0003_UDMACKUnsolicitedDisplayUpdateMessage,
  QBPQueryByParameterRequestingAnRSPSegmentPatternResponse: v2_0003_QBPQueryByParameterRequestingAnRSPSegmentPatternResponse,
  QBPQueryByParameterRequestingAnRTBTabularResponse: v2_0003_QBPQueryByParameterRequestingAnRTBTabularResponse,
  QBPQueryByParameterRequestingAnRDYDisplayResponse: v2_0003_QBPQueryByParameterRequestingAnRDYDisplayResponse,
  QSBCreateSubscription: v2_0003_QSBCreateSubscription,
  QVRQueryForPreviousEvents: v2_0003_QVRQueryForPreviousEvents,
  QBPGetPersonDemographics: v2_0003_QBPGetPersonDemographics,
  QBPFindCandidates: v2_0003_QBPFindCandidates,
  QBPGetCorrespondingIdentifiers: v2_0003_QBPGetCorrespondingIdentifiers,
  QBPAllocateIdentifiers: v2_0003_QBPAllocateIdentifiers,
  QBPPersonnelInformationBySegmentQuery: v2_0003_QBPPersonnelInformationBySegmentQuery,
  QBPQueryDispenseHistory: v2_0003_QBPQueryDispenseHistory,
  FindCandidatesIncludingVisitInformation: v2_0003_FindCandidatesIncludingVisitInformation,
  QBPGetDonorRecordCandidates: v2_0003_QBPGetDonorRecordCandidates,
  QBPGetDonorRecord: v2_0003_QBPGetDonorRecord,
  ORUACKUnsolicitedTransmissionOfAnObservationMessage: v2_0003_ORUACKUnsolicitedTransmissionOfAnObservationMessage,
  OULUnsolicitedSpecimenOrientedObservationMessage: v2_0003_OULUnsolicitedSpecimenOrientedObservationMessage,
  OULUnsolicitedSpecimenContainerOrientedObservationMessage: v2_0003_OULUnsolicitedSpecimenContainerOrientedObservationMessage,
  OULUnsolicitedOrderOrientedObservationMessage: v2_0003_OULUnsolicitedOrderOrientedObservationMessage,
  OPUUnsolicitedPopulationLocationBasedLaboratoryObservationMessage: v2_0003_OPUUnsolicitedPopulationLocationBasedLaboratoryObservationMessage,
  OSMUnsolicitedSpecimenShipmentManifestMessage: v2_0003_OSMUnsolicitedSpecimenShipmentManifestMessage,
  ORUUnsolicitedPointOfCareObservationMessageWithoutExistingOrderPlaceAnOrder: v2_0003_ORUUnsolicitedPointOfCareObservationMessageWithoutExistingOrderPlaceAnOrder,
  ORUUnsolicitedNewPointOfCareObservationMessageSearchForAnOrder: v2_0003_ORUUnsolicitedNewPointOfCareObservationMessageSearchForAnOrder,
  ORUUnsolicitedPreOrderedPointOfCareObservation: v2_0003_ORUUnsolicitedPreOrderedPointOfCareObservation,
  ORAObservationReportAcknowledgement: v2_0003_ORAObservationReportAcknowledgement,
  ORUUnsolicitedReportAlarm: v2_0003_ORUUnsolicitedReportAlarm,
  ObservationReportAlertAcknowledgement: v2_0003_ObservationReportAlertAcknowledgement,
  ORUUnsolicitedDeviceEventObservationMessage: v2_0003_ORUUnsolicitedDeviceEventObservationMessage,
  ORUUnsolicitedPatientDeviceAssociationObservationMessage: v2_0003_ORUUnsolicitedPatientDeviceAssociationObservationMessage,
  SRMSRRRequestNewAppointmentBooking: v2_0003_SRMSRRRequestNewAppointmentBooking,
  SRMSRRRequestAppointmentRescheduling: v2_0003_SRMSRRRequestAppointmentRescheduling,
  SRMSRRRequestAppointmentModification: v2_0003_SRMSRRRequestAppointmentModification,
  SRMSRRRequestAppointmentCancellation: v2_0003_SRMSRRRequestAppointmentCancellation,
  SRMSRRRequestAppointmentDiscontinuation: v2_0003_SRMSRRRequestAppointmentDiscontinuation,
  SRMSRRRequestAppointmentDeletion: v2_0003_SRMSRRRequestAppointmentDeletion,
  SRMSRRRequestAdditionOfServiceResourceOnAppointment: v2_0003_SRMSRRRequestAdditionOfServiceResourceOnAppointment,
  SRMSRRRequestModificationOfServiceResourceOnAppointment: v2_0003_SRMSRRRequestModificationOfServiceResourceOnAppointment,
  SRMSRRRequestCancellationOfServiceResourceOnAppointment: v2_0003_SRMSRRRequestCancellationOfServiceResourceOnAppointment,
  SRMSRRRequestDiscontinuationOfServiceResourceOnAppointment: v2_0003_SRMSRRRequestDiscontinuationOfServiceResourceOnAppointment,
  SRMSRRRequestDeletionOfServiceResourceOnAppointment: v2_0003_SRMSRRRequestDeletionOfServiceResourceOnAppointment,
  SIUACKNotificationOfNewAppointmentBooking: v2_0003_SIUACKNotificationOfNewAppointmentBooking,
  SIUACKNotificationOfAppointmentRescheduling: v2_0003_SIUACKNotificationOfAppointmentRescheduling,
  SIUACKNotificationOfAppointmentModification: v2_0003_SIUACKNotificationOfAppointmentModification,
  SIUACKNotificationOfAppointmentCancellation: v2_0003_SIUACKNotificationOfAppointmentCancellation,
  SIUACKNotificationOfAppointmentDiscontinuation: v2_0003_SIUACKNotificationOfAppointmentDiscontinuation,
  SIUACKNotificationOfAppointmentDeletion: v2_0003_SIUACKNotificationOfAppointmentDeletion,
  SIUACKNotificationOfAdditionOfServiceResourceOnAppointment: v2_0003_SIUACKNotificationOfAdditionOfServiceResourceOnAppointment,
  SIUACKNotificationOfModificationOfServiceResourceOnAppointment: v2_0003_SIUACKNotificationOfModificationOfServiceResourceOnAppointment,
  SIUACKNotificationOfCancellationOfServiceResourceOnAppointment: v2_0003_SIUACKNotificationOfCancellationOfServiceResourceOnAppointment,
  SIUACKNotificationOfDiscontinuationOfServiceResourceOnAppointment: v2_0003_SIUACKNotificationOfDiscontinuationOfServiceResourceOnAppointment,
  SIUACKNotificationOfDeletionOfServiceResourceOnAppointment: v2_0003_SIUACKNotificationOfDeletionOfServiceResourceOnAppointment,
  SIUACKNotificationOfBlockedScheduleTimeSlotS: v2_0003_SIUACKNotificationOfBlockedScheduleTimeSlotS,
  SIUACKNotificationOfOpenedQuotationUnblockedQuotationScheduleTimeSlotS: v2_0003_SIUACKNotificationOfOpenedQuotationUnblockedQuotationScheduleTimeSlotS,
  SIUACKNotificationThatPatientDidNotShowUpForScheduleAppointment: v2_0003_SIUACKNotificationThatPatientDidNotShowUpForScheduleAppointment,
  SIUACKBroadcastNotificationOfScheduledAppointments: v2_0003_SIUACKBroadcastNotificationOfScheduledAppointments,
  SLRSLSRequestNewSterilizationLot: v2_0003_SLRSLSRequestNewSterilizationLot,
  SLRSLSRequestSterilizationLotDeletion: v2_0003_SLRSLSRequestSterilizationLotDeletion,
  STISTSRequestItem: v2_0003_STISTSRequestItem,
  SDRSDSRequestAntiMicrobialDeviceData: v2_0003_SDRSDSRequestAntiMicrobialDeviceData,
  SMDSMSRequestAntiMicrobialDeviceCycleData: v2_0003_SMDSMSRequestAntiMicrobialDeviceCycleData,
  STCACKNotificationOfSterilizationConfiguration: v2_0003_STCACKNotificationOfSterilizationConfiguration,
  SLNACKNotificationOfSterilizationLot: v2_0003_SLNACKNotificationOfSterilizationLot,
  SLNACKNotificationOfSterilizationLotDeletion: v2_0003_SLNACKNotificationOfSterilizationLotDeletion,
  SDNACKNotificationOfAntiMicrobialDeviceData: v2_0003_SDNACKNotificationOfAntiMicrobialDeviceData,
  SCNACKNotificationOfAntiMicrobialDeviceCycleData: v2_0003_SCNACKNotificationOfAntiMicrobialDeviceCycleData,
  MDMACKOriginalDocumentNotification: v2_0003_MDMACKOriginalDocumentNotification,
  MDMACKOriginalDocumentNotificationAndContent: v2_0003_MDMACKOriginalDocumentNotificationAndContent,
  MDMACKDocumentStatusChangeNotification: v2_0003_MDMACKDocumentStatusChangeNotification,
  MDMACKDocumentStatusChangeNotificationAndContent: v2_0003_MDMACKDocumentStatusChangeNotificationAndContent,
  MDMACKDocumentAddendumNotification: v2_0003_MDMACKDocumentAddendumNotification,
  MDMACKDocumentAddendumNotificationAndContent: v2_0003_MDMACKDocumentAddendumNotificationAndContent,
  MDMACKDocumentEditNotification: v2_0003_MDMACKDocumentEditNotification,
  MDMACKDocumentEditNotificationAndContent: v2_0003_MDMACKDocumentEditNotificationAndContent,
  MDMACKDocumentReplacementNotification: v2_0003_MDMACKDocumentReplacementNotification,
  MDMACKDocumentReplacementNotificationAndContent: v2_0003_MDMACKDocumentReplacementNotificationAndContent,
  MDMACKDocumentCancelNotification: v2_0003_MDMACKDocumentCancelNotification,
  ESUACKAutomatedEquipmentStatusUpdate: v2_0003_ESUACKAutomatedEquipmentStatusUpdate,
  ESRACKAutomatedEquipmentStatusRequest: v2_0003_ESRACKAutomatedEquipmentStatusRequest,
  SSUACKSpecimenStatusUpdate: v2_0003_SSUACKSpecimenStatusUpdate,
  SSRACKSpecimenStatusRequest: v2_0003_SSRACKSpecimenStatusRequest,
  INUACKAutomatedEquipmentInventoryUpdate: v2_0003_INUACKAutomatedEquipmentInventoryUpdate,
  INRACKAutomatedEquipmentInventoryRequest: v2_0003_INRACKAutomatedEquipmentInventoryRequest,
  EACACKAutomatedEquipmentCommand: v2_0003_EACACKAutomatedEquipmentCommand,
  EARACKAutomatedEquipmentResponse: v2_0003_EARACKAutomatedEquipmentResponse,
  EANACKAutomatedEquipmentNotification: v2_0003_EANACKAutomatedEquipmentNotification,
  TCUACKAutomatedEquipmentTestCodeSettingsUpdate: v2_0003_TCUACKAutomatedEquipmentTestCodeSettingsUpdate,
  TCRACKAutomatedEquipmentTestCodeSettingsRequest: v2_0003_TCRACKAutomatedEquipmentTestCodeSettingsRequest,
  LSUACKAutomatedEquipmentLogServiceUpdate: v2_0003_LSUACKAutomatedEquipmentLogServiceUpdate,
  LSRACKAutomatedEquipmentLogServiceRequest: v2_0003_LSRACKAutomatedEquipmentLogServiceRequest,
  INRACKAutomatedEquipmentInventoryRequest_2: v2_0003_INRACKAutomatedEquipmentInventoryRequest_2,
  VXUUnsolicitedVaccinationRecordUpdate: v2_0003_VXUUnsolicitedVaccinationRecordUpdate,
  MFQMFRMasterFilesQueryUseEventSameAsAskingForEGM05Location: v2_0003_MFQMFRMasterFilesQueryUseEventSameAsAskingForEGM05Location,
};
const v2_0004_Obstetrics: Coding = {
  code: 'B',
  display: 'Obstetrics',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0004',
};
const v2_0004_CommercialAccount: Coding = {
  code: 'C',
  display: 'Commercial Account',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0004',
};
const v2_0004_Emergency: Coding = {
  code: 'E',
  display: 'Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0004',
};
const v2_0004_Inpatient: Coding = {
  code: 'I',
  display: 'Inpatient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0004',
};
const v2_0004_NotApplicable: Coding = {
  code: 'N',
  display: 'Not Applicable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0004',
};
const v2_0004_Outpatient: Coding = {
  code: 'O',
  display: 'Outpatient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0004',
};
const v2_0004_Preadmit: Coding = {
  code: 'P',
  display: 'Preadmit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0004',
};
const v2_0004_RecurringPatient: Coding = {
  code: 'R',
  display: 'Recurring patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0004',
};
const v2_0004_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0004',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0004 ( PATIENT CLASS)
 */
export const V20004 = {
  Obstetrics: v2_0004_Obstetrics,
  CommercialAccount: v2_0004_CommercialAccount,
  Emergency: v2_0004_Emergency,
  Inpatient: v2_0004_Inpatient,
  NotApplicable: v2_0004_NotApplicable,
  Outpatient: v2_0004_Outpatient,
  Preadmit: v2_0004_Preadmit,
  RecurringPatient: v2_0004_RecurringPatient,
  Unknown: v2_0004_Unknown,
};
const v3_Race_AmericanIndianOrAlaskaNative: Coding = {
  code: '1002-5',
  display: 'American Indian or Alaska Native',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Asian: Coding = {
  code: '2028-9',
  display: 'Asian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BlackOrAfricanAmerican: Coding = {
  code: '2054-5',
  display: 'Black or African American',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NativeHawaiianOrOtherPacificIslander: Coding = {
  code: '2076-8',
  display: 'Native Hawaiian or Other Pacific Islander',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_White: Coding = {
  code: '2106-3',
  display: 'White',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OtherRace: Coding = {
  code: '2131-1',
  display: 'Other Race',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0005 ( ETHNIC GROUP)
 */
export const V20005 = {
  /**
   * American Indian or Alaska Native
   */
  AmericanIndianOrAlaskaNative: v3_Race_AmericanIndianOrAlaskaNative,
  /**
   * Asian
   */
  Asian: v3_Race_Asian,
  /**
   * Black or African American
   */
  BlackOrAfricanAmerican: v3_Race_BlackOrAfricanAmerican,
  /**
   * Native Hawaiian or Other Pacific Islander
   */
  NativeHawaiianOrOtherPacificIslander: v3_Race_NativeHawaiianOrOtherPacificIslander,
  /**
   * White
   */
  White: v3_Race_White,
  /**
   * Note that this term remains in the table for completeness, even though within HL7, the notion of Other code is deprecated.
   */
  OtherRace: v3_Race_OtherRace,
};
const v2_0007_Accident: Coding = {
  code: 'A',
  display: 'Accident',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0007',
};
const v2_0007_Elective: Coding = {
  code: 'C',
  display: 'Elective',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0007',
};
const v2_0007_Emergency: Coding = {
  code: 'E',
  display: 'Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0007',
};
const v2_0007_LaborAndDelivery: Coding = {
  code: 'L',
  display: 'Labor and Delivery',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0007',
};
const v2_0007_NewbornBirthInHealthcareFacility: Coding = {
  code: 'N',
  display: 'Newborn (Birth in healthcare facility)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0007',
};
const v2_0007_Routine: Coding = {
  code: 'R',
  display: 'Routine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0007',
};
const v2_0007_Urgent: Coding = {
  code: 'U',
  display: 'Urgent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0007',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0007 ( ADMISSION TYPE)
 */
export const V20007 = {
  Accident: v2_0007_Accident,
  Elective: v2_0007_Elective,
  Emergency: v2_0007_Emergency,
  LaborAndDelivery: v2_0007_LaborAndDelivery,
  NewbornBirthInHealthcareFacility: v2_0007_NewbornBirthInHealthcareFacility,
  Routine: v2_0007_Routine,
  Urgent: v2_0007_Urgent,
};
const v2_0008_OriginalModeApplicationAcceptEnhancedModeApplicationAcknowledgmentAccept: Coding = {
  code: 'AA',
  display: 'Original mode: Application Accept - Enhanced mode: Application acknowledgment: Accept',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0008',
};
const v2_0008_OriginalModeApplicationErrorEnhancedModeApplicationAcknowledgmentError: Coding = {
  code: 'AE',
  display: 'Original mode: Application Error - Enhanced mode: Application acknowledgment: Error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0008',
};
const v2_0008_OriginalModeApplicationRejectEnhancedModeApplicationAcknowledgmentReject: Coding = {
  code: 'AR',
  display: 'Original mode: Application Reject - Enhanced mode: Application acknowledgment: Reject',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0008',
};
const v2_0008_EnhancedModeAcceptAcknowledgmentCommitAccept: Coding = {
  code: 'CA',
  display: 'Enhanced mode: Accept acknowledgment: Commit Accept',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0008',
};
const v2_0008_EnhancedModeAcceptAcknowledgmentCommitError: Coding = {
  code: 'CE',
  display: 'Enhanced mode: Accept acknowledgment: Commit Error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0008',
};
const v2_0008_EnhancedModeAcceptAcknowledgmentCommitReject: Coding = {
  code: 'CR',
  display: 'Enhanced mode: Accept acknowledgment: Commit Reject',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0008',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0008 ( ACKNOWLEDGMENT CODE)
 */
export const V20008 = {
  OriginalModeApplicationAcceptEnhancedModeApplicationAcknowledgmentAccept: v2_0008_OriginalModeApplicationAcceptEnhancedModeApplicationAcknowledgmentAccept,
  OriginalModeApplicationErrorEnhancedModeApplicationAcknowledgmentError: v2_0008_OriginalModeApplicationErrorEnhancedModeApplicationAcknowledgmentError,
  OriginalModeApplicationRejectEnhancedModeApplicationAcknowledgmentReject: v2_0008_OriginalModeApplicationRejectEnhancedModeApplicationAcknowledgmentReject,
  EnhancedModeAcceptAcknowledgmentCommitAccept: v2_0008_EnhancedModeAcceptAcknowledgmentCommitAccept,
  EnhancedModeAcceptAcknowledgmentCommitError: v2_0008_EnhancedModeAcceptAcknowledgmentCommitError,
  EnhancedModeAcceptAcknowledgmentCommitReject: v2_0008_EnhancedModeAcceptAcknowledgmentCommitReject,
};
const v2_0009_NoFunctionalLimitations: Coding = {
  code: 'A0',
  display: 'No functional limitations',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_AmbulatesWithAssistiveDevice: Coding = {
  code: 'A1',
  display: 'Ambulates with assistive device',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_WheelchairStretcherBound: Coding = {
  code: 'A2',
  display: 'Wheelchair/stretcher bound',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_ComatoseNonResponsive: Coding = {
  code: 'A3',
  display: 'Comatose; non-responsive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_Disoriented: Coding = {
  code: 'A4',
  display: 'Disoriented',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_VisionImpaired: Coding = {
  code: 'A5',
  display: 'Vision impaired',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_HearingImpaired: Coding = {
  code: 'A6',
  display: 'Hearing impaired',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_SpeechImpaired: Coding = {
  code: 'A7',
  display: 'Speech impaired',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_NonEnglishSpeaking: Coding = {
  code: 'A8',
  display: 'Non-English speaking',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_FunctionalLevelUnknown: Coding = {
  code: 'A9',
  display: 'Functional level unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_OxygenTherapy: Coding = {
  code: 'B1',
  display: 'Oxygen therapy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_SpecialEquipmentTubesIVsCatheters: Coding = {
  code: 'B2',
  display: 'Special equipment (tubes, IVs, catheters)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_Amputee: Coding = {
  code: 'B3',
  display: 'Amputee',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_Mastectomy: Coding = {
  code: 'B4',
  display: 'Mastectomy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_Paraplegic: Coding = {
  code: 'B5',
  display: 'Paraplegic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
const v2_0009_Pregnant: Coding = {
  code: 'B6',
  display: 'Pregnant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0009',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0009 ( AMBULATORY STATUS)
 */
export const V20009 = {
  NoFunctionalLimitations: v2_0009_NoFunctionalLimitations,
  AmbulatesWithAssistiveDevice: v2_0009_AmbulatesWithAssistiveDevice,
  WheelchairStretcherBound: v2_0009_WheelchairStretcherBound,
  ComatoseNonResponsive: v2_0009_ComatoseNonResponsive,
  Disoriented: v2_0009_Disoriented,
  VisionImpaired: v2_0009_VisionImpaired,
  HearingImpaired: v2_0009_HearingImpaired,
  SpeechImpaired: v2_0009_SpeechImpaired,
  NonEnglishSpeaking: v2_0009_NonEnglishSpeaking,
  FunctionalLevelUnknown: v2_0009_FunctionalLevelUnknown,
  OxygenTherapy: v2_0009_OxygenTherapy,
  SpecialEquipmentTubesIVsCatheters: v2_0009_SpecialEquipmentTubesIVsCatheters,
  Amputee: v2_0009_Amputee,
  Mastectomy: v2_0009_Mastectomy,
  Paraplegic: v2_0009_Paraplegic,
  Pregnant: v2_0009_Pregnant,
};
const v2_0017_Adjustment: Coding = {
  code: 'AJ',
  display: 'Adjustment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0017',
};
const v2_0017_Credit: Coding = {
  code: 'CD',
  display: 'Credit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0017',
};
const v2_0017_Charge: Coding = {
  code: 'CG',
  display: 'Charge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0017',
};
const v2_0017_CoPayment: Coding = {
  code: 'CO',
  display: 'Co-payment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0017',
};
const v2_0017_Payment: Coding = {
  code: 'PY',
  display: 'Payment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0017',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0017 ( Transaction Type)
 */
export const V20017 = {
  Adjustment: v2_0017_Adjustment,
  Credit: v2_0017_Credit,
  Charge: v2_0017_Charge,
  CoPayment: v2_0017_CoPayment,
  Payment: v2_0017_Payment,
};
const v2_0023_NoSuggestedValuesDefined: Coding = {
  code: '...',
  display: 'No suggested values defined',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0023',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0023 ( Admit source)
 */
export const V20023 = {
  NoSuggestedValuesDefined: v2_0023_NoSuggestedValuesDefined,
};
const v2_0027_AsSoonAsPossibleAPriorityLowerThanStat: Coding = {
  code: 'A',
  display: 'As soon as possible (a priority lower than stat)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0027',
};
const v2_0027_PreoperativeToBeDonePriorToSurgery: Coding = {
  code: 'P',
  display: 'Preoperative (to be done prior to surgery)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0027',
};
const v2_0027_Routine: Coding = {
  code: 'R',
  display: 'Routine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0027',
};
const v2_0027_StatDoImmediately: Coding = {
  code: 'S',
  display: 'Stat (do immediately)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0027',
};
const v2_0027_TimingCriticalDoAsNearAsPossibleToRequestedTime: Coding = {
  code: 'T',
  display: 'Timing critical (do as near as possible to requested time)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0027',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0027 ( PRIORITY (COMPONENT 6 QTY/TIMING[735]))
 */
export const V20027 = {
  AsSoonAsPossibleAPriorityLowerThanStat: v2_0027_AsSoonAsPossibleAPriorityLowerThanStat,
  PreoperativeToBeDonePriorToSurgery: v2_0027_PreoperativeToBeDonePriorToSurgery,
  Routine: v2_0027_Routine,
  StatDoImmediately: v2_0027_StatDoImmediately,
  TimingCriticalDoAsNearAsPossibleToRequestedTime: v2_0027_TimingCriticalDoAsNearAsPossibleToRequestedTime,
};
const v2_0038_SomeButNotAllResultsAvailable: Coding = {
  code: 'A',
  display: 'Some, but not all, results available',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0038',
};
const v2_0038_OrderWasCanceled: Coding = {
  code: 'CA',
  display: 'Order was canceled',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0038',
};
const v2_0038_OrderIsCompleted: Coding = {
  code: 'CM',
  display: 'Order is completed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0038',
};
const v2_0038_OrderWasDiscontinued: Coding = {
  code: 'DC',
  display: 'Order was discontinued',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0038',
};
const v2_0038_ErrorOrderNotFound: Coding = {
  code: 'ER',
  display: 'Error, order not found',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0038',
};
const v2_0038_OrderIsOnHold: Coding = {
  code: 'HD',
  display: 'Order is on hold',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0038',
};
const v2_0038_InProcessUnspecified: Coding = {
  code: 'IP',
  display: 'In process, unspecified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0038',
};
const v2_0038_OrderHasBeenReplaced: Coding = {
  code: 'RP',
  display: 'Order has been replaced',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0038',
};
const v2_0038_InProcessScheduled: Coding = {
  code: 'SC',
  display: 'In process, scheduled',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0038',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0038 ( ORDER STATUS)
 */
export const V20038 = {
  SomeButNotAllResultsAvailable: v2_0038_SomeButNotAllResultsAvailable,
  OrderWasCanceled: v2_0038_OrderWasCanceled,
  OrderIsCompleted: v2_0038_OrderIsCompleted,
  OrderWasDiscontinued: v2_0038_OrderWasDiscontinued,
  ErrorOrderNotFound: v2_0038_ErrorOrderNotFound,
  OrderIsOnHold: v2_0038_OrderIsOnHold,
  InProcessUnspecified: v2_0038_InProcessUnspecified,
  OrderHasBeenReplaced: v2_0038_OrderHasBeenReplaced,
  InProcessScheduled: v2_0038_InProcessScheduled,
};
const v2_0043_NoSuggestedValuesDefined: Coding = {
  code: '...',
  display: 'No suggested values defined',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0043',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0043 ( Condition code)
 */
export const V20043 = {
  NoSuggestedValuesDefined: v2_0043_NoSuggestedValuesDefined,
};
const v2_0052_Admitting: Coding = {
  code: 'A',
  display: 'Admitting',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0052',
};
const v2_0052_Final: Coding = {
  code: 'F',
  display: 'Final',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0052',
};
const v2_0052_Working: Coding = {
  code: 'W',
  display: 'Working',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0052',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0052 ( Diagnosis Type)
 */
export const V20052 = {
  Admitting: v2_0052_Admitting,
  Final: v2_0052_Final,
  Working: v2_0052_Working,
};
const v2_0061_BankCardValidationNumber: Coding = {
  code: 'BCV',
  display: 'Bank Card Validation Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0061',
};
const v2_0061_ISO70641983: Coding = {
  code: 'ISO',
  display: 'ISO 7064: 1983',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0061',
};
const v2_0061_Mod10Algorithm: Coding = {
  code: 'M10',
  display: 'Mod 10 algorithm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0061',
};
const v2_0061_Mod11Algorithm: Coding = {
  code: 'M11',
  display: 'Mod 11 algorithm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0061',
};
const v2_0061_CheckDigitAlgorithmInTheUSNationalProviderIdentifier: Coding = {
  code: 'NPI',
  display: 'Check digit algorithm in the US National Provider Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0061',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0061 ( CHECK DIGIT SCHEME)
 */
export const V20061 = {
  BankCardValidationNumber: v2_0061_BankCardValidationNumber,
  ISO70641983: v2_0061_ISO70641983,
  Mod10Algorithm: v2_0061_Mod10Algorithm,
  Mod11Algorithm: v2_0061_Mod11Algorithm,
  CheckDigitAlgorithmInTheUSNationalProviderIdentifier: v2_0061_CheckDigitAlgorithmInTheUSNationalProviderIdentifier,
};
const v2_0062_PatientRequest: Coding = {
  code: '01',
  display: 'Patient request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0062',
};
const v2_0062_PhysicianHealthPractitionerOrder: Coding = {
  code: '02',
  display: 'Physician/health practitioner order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0062',
};
const v2_0062_CensusManagement: Coding = {
  code: '03',
  display: 'Census management',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0062',
};
const v2_0062_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0062',
};
const v2_0062_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0062',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0062 ( EVENT REASON)
 */
export const V20062 = {
  PatientRequest: v2_0062_PatientRequest,
  PhysicianHealthPractitionerOrder: v2_0062_PhysicianHealthPractitionerOrder,
  CensusManagement: v2_0062_CensusManagement,
  Other: v2_0062_Other,
  Unknown: v2_0062_Unknown,
};
const v2_0063_Associate: Coding = {
  code: 'ASC',
  display: 'Associate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Brother: Coding = {
  code: 'BRO',
  display: 'Brother',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_CareGiver: Coding = {
  code: 'CGV',
  display: 'Care giver',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Child: Coding = {
  code: 'CHD',
  display: 'Child',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_HandicappedDependent: Coding = {
  code: 'DEP',
  display: 'Handicapped dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_LifePartner: Coding = {
  code: 'DOM',
  display: 'Life partner',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_EmergencyContact: Coding = {
  code: 'EMC',
  display: 'Emergency contact',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Employee: Coding = {
  code: 'EME',
  display: 'Employee',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Employer: Coding = {
  code: 'EMR',
  display: 'Employer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_ExtendedFamily: Coding = {
  code: 'EXF',
  display: 'Extended family',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_FosterChild: Coding = {
  code: 'FCH',
  display: 'Foster child',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Friend: Coding = {
  code: 'FND',
  display: 'Friend',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Father: Coding = {
  code: 'FTH',
  display: 'Father',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Grandchild: Coding = {
  code: 'GCH',
  display: 'Grandchild',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Guardian: Coding = {
  code: 'GRD',
  display: 'Guardian',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Grandparent: Coding = {
  code: 'GRP',
  display: 'Grandparent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Manager: Coding = {
  code: 'MGR',
  display: 'Manager',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Mother: Coding = {
  code: 'MTH',
  display: 'Mother',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_NaturalChild: Coding = {
  code: 'NCH',
  display: 'Natural child',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_None: Coding = {
  code: 'NON',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_OtherAdult: Coding = {
  code: 'OAD',
  display: 'Other adult',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Other: Coding = {
  code: 'OTH',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Owner: Coding = {
  code: 'OWN',
  display: 'Owner',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Parent: Coding = {
  code: 'PAR',
  display: 'Parent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Stepchild: Coding = {
  code: 'SCH',
  display: 'Stepchild',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Self: Coding = {
  code: 'SEL',
  display: 'Self',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Sibling: Coding = {
  code: 'SIB',
  display: 'Sibling',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Sister: Coding = {
  code: 'SIS',
  display: 'Sister',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Spouse: Coding = {
  code: 'SPO',
  display: 'Spouse',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Trainer: Coding = {
  code: 'TRA',
  display: 'Trainer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_Unknown: Coding = {
  code: 'UNK',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
const v2_0063_WardOfCourt: Coding = {
  code: 'WRD',
  display: 'Ward of court',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0063',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0063 ( Relationship)
 */
export const V20063 = {
  Associate: v2_0063_Associate,
  Brother: v2_0063_Brother,
  CareGiver: v2_0063_CareGiver,
  Child: v2_0063_Child,
  HandicappedDependent: v2_0063_HandicappedDependent,
  LifePartner: v2_0063_LifePartner,
  EmergencyContact: v2_0063_EmergencyContact,
  Employee: v2_0063_Employee,
  Employer: v2_0063_Employer,
  ExtendedFamily: v2_0063_ExtendedFamily,
  FosterChild: v2_0063_FosterChild,
  Friend: v2_0063_Friend,
  Father: v2_0063_Father,
  Grandchild: v2_0063_Grandchild,
  Guardian: v2_0063_Guardian,
  Grandparent: v2_0063_Grandparent,
  Manager: v2_0063_Manager,
  Mother: v2_0063_Mother,
  NaturalChild: v2_0063_NaturalChild,
  None: v2_0063_None,
  OtherAdult: v2_0063_OtherAdult,
  Other: v2_0063_Other,
  Owner: v2_0063_Owner,
  Parent: v2_0063_Parent,
  Stepchild: v2_0063_Stepchild,
  Self: v2_0063_Self,
  Sibling: v2_0063_Sibling,
  Sister: v2_0063_Sister,
  Spouse: v2_0063_Spouse,
  Trainer: v2_0063_Trainer,
  Unknown: v2_0063_Unknown,
  WardOfCourt: v2_0063_WardOfCourt,
};
const v2_0065_AddOrderedTestsToTheExistingSpecimen: Coding = {
  code: 'A',
  display: 'Add ordered tests to the existing specimen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0065',
};
const v2_0065_GeneratedOrderReflexOrder: Coding = {
  code: 'G',
  display: 'Generated order; reflex order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0065',
};
const v2_0065_LabToObtainSpecimenFromPatient: Coding = {
  code: 'L',
  display: 'Lab to obtain specimen from patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0065',
};
const v2_0065_SpecimenObtainedByServiceOtherThanLab: Coding = {
  code: 'O',
  display: 'Specimen obtained by service other than Lab',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0065',
};
const v2_0065_PendingSpecimenOrderSentPriorToDelivery: Coding = {
  code: 'P',
  display: 'Pending specimen; Order sent prior to delivery',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0065',
};
const v2_0065_RevisedOrder: Coding = {
  code: 'R',
  display: 'Revised order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0065',
};
const v2_0065_ScheduleTheTestsSpecifiedBelow: Coding = {
  code: 'S',
  display: 'Schedule the tests specified below',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0065',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0065 ( ACTION CODE)
 */
export const V20065 = {
  AddOrderedTestsToTheExistingSpecimen: v2_0065_AddOrderedTestsToTheExistingSpecimen,
  GeneratedOrderReflexOrder: v2_0065_GeneratedOrderReflexOrder,
  LabToObtainSpecimenFromPatient: v2_0065_LabToObtainSpecimenFromPatient,
  SpecimenObtainedByServiceOtherThanLab: v2_0065_SpecimenObtainedByServiceOtherThanLab,
  PendingSpecimenOrderSentPriorToDelivery: v2_0065_PendingSpecimenOrderSentPriorToDelivery,
  RevisedOrder: v2_0065_RevisedOrder,
  ScheduleTheTestsSpecifiedBelow: v2_0065_ScheduleTheTestsSpecifiedBelow,
};
const v2_0066_FullTimeEmployed: Coding = {
  code: '1',
  display: 'Full time employed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0066',
};
const v2_0066_PartTimeEmployed: Coding = {
  code: '2',
  display: 'Part time employed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0066',
};
const v2_0066_Unemployed: Coding = {
  code: '3',
  display: 'Unemployed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0066',
};
const v2_0066_SelfEmployed: Coding = {
  code: '4',
  display: 'Self-employed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0066',
};
const v2_0066_Retired: Coding = {
  code: '5',
  display: 'Retired',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0066',
};
const v2_0066_OnActiveMilitaryDuty: Coding = {
  code: '6',
  display: 'On active military duty',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0066',
};
const v2_0066_Unknown: Coding = {
  code: '9',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0066',
};
const v2_0066_ContractPerDiem: Coding = {
  code: 'C',
  display: 'Contract, per diem',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0066',
};
const v2_0066_LeaveOfAbsenceEGFamilyLeaveSabbaticalEtc: Coding = {
  code: 'L',
  display: 'Leave of absence (e.g., family leave, sabbatical, etc.)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0066',
};
const v2_0066_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0066',
};
const v2_0066_TemporarilyUnemployed: Coding = {
  code: 'T',
  display: 'Temporarily unemployed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0066',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0066 ( Employment status)
 */
export const V20066 = {
  FullTimeEmployed: v2_0066_FullTimeEmployed,
  PartTimeEmployed: v2_0066_PartTimeEmployed,
  Unemployed: v2_0066_Unemployed,
  SelfEmployed: v2_0066_SelfEmployed,
  Retired: v2_0066_Retired,
  OnActiveMilitaryDuty: v2_0066_OnActiveMilitaryDuty,
  Unknown: v2_0066_Unknown,
  ContractPerDiem: v2_0066_ContractPerDiem,
  LeaveOfAbsenceEGFamilyLeaveSabbaticalEtc: v2_0066_LeaveOfAbsenceEGFamilyLeaveSabbaticalEtc,
  Other: v2_0066_Other,
  TemporarilyUnemployed: v2_0066_TemporarilyUnemployed,
};
const v2_0069_CardiacService: Coding = {
  code: 'CAR',
  display: 'Cardiac Service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0069',
};
const v2_0069_MedicalService: Coding = {
  code: 'MED',
  display: 'Medical Service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0069',
};
const v2_0069_PulmonaryService: Coding = {
  code: 'PUL',
  display: 'Pulmonary Service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0069',
};
const v2_0069_SurgicalService: Coding = {
  code: 'SUR',
  display: 'Surgical Service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0069',
};
const v2_0069_UrologyService: Coding = {
  code: 'URO',
  display: 'Urology Service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0069',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0069 ( Hospital service)
 */
export const V20069 = {
  CardiacService: v2_0069_CardiacService,
  MedicalService: v2_0069_MedicalService,
  PulmonaryService: v2_0069_PulmonaryService,
  SurgicalService: v2_0069_SurgicalService,
  UrologyService: v2_0069_UrologyService,
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0074 ( DIAGNOSTIC SERVICE SECTION ID)
 */
export const V20074 = {
  Audiology: v2_0074_Audiology,
  BloodGases: v2_0074_BloodGases,
  BloodBank: v2_0074_BloodBank,
  Cytogenetics: v2_0074_Cytogenetics,
  Chemistry: v2_0074_Chemistry,
  Cytopathology: v2_0074_Cytopathology,
  CATScan: v2_0074_CATScan,
  CardiacCatheterization: v2_0074_CardiacCatheterization,
  CardiacUltrasound: v2_0074_CardiacUltrasound,
  ElectrocardiacEGEKGEECHolter: v2_0074_ElectrocardiacEGEKGEECHolter,
  ElectroneuroEEGEMGEPPSG: v2_0074_ElectroneuroEEGEMGEPPSG,
  Genetics: v2_0074_Genetics,
  Hematology: v2_0074_Hematology,
  BedsideICUMonitoring: v2_0074_BedsideICUMonitoring,
  Immunology: v2_0074_Immunology,
  Laboratory: v2_0074_Laboratory,
  Microbiology: v2_0074_Microbiology,
  Mycobacteriology: v2_0074_Mycobacteriology,
  Mycology: v2_0074_Mycology,
  NuclearMagneticResonance: v2_0074_NuclearMagneticResonance,
  NuclearMedicineScan: v2_0074_NuclearMedicineScan,
  NursingServiceMeasures: v2_0074_NursingServiceMeasures,
  OutsideLab: v2_0074_OutsideLab,
  OccupationalTherapy: v2_0074_OccupationalTherapy,
  Other: v2_0074_Other,
  OBUltrasound: v2_0074_OBUltrasound,
  PulmonaryFunction: v2_0074_PulmonaryFunction,
  Pharmacy: v2_0074_Pharmacy,
  PhysicianHxDxAdmissionNoteEtc: v2_0074_PhysicianHxDxAdmissionNoteEtc,
  PhysicalTherapy: v2_0074_PhysicalTherapy,
  Radiology: v2_0074_Radiology,
  RespiratoryCareTherapy: v2_0074_RespiratoryCareTherapy,
  RadiationTherapy: v2_0074_RadiationTherapy,
  RadiologyUltrasound: v2_0074_RadiologyUltrasound,
  Radiograph: v2_0074_Radiograph,
  SurgicalPathology: v2_0074_SurgicalPathology,
  Serology: v2_0074_Serology,
  Toxicology: v2_0074_Toxicology,
  Virology: v2_0074_Virology,
  VascularUltrasound: v2_0074_VascularUltrasound,
  Cineradiograph: v2_0074_Cineradiograph,
};
const v2_0076_GeneralAcknowledgmentMessage: Coding = {
  code: 'ACK',
  display: 'General acknowledgment message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ADTResponse: Coding = {
  code: 'ADR',
  display: 'ADT response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ADTMessage: Coding = {
  code: 'ADT',
  display: 'ADT message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_AddChangeBillingAccount: Coding = {
  code: 'BAR',
  display: 'Add/change billing account',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_BloodProductDispenseStatusMessage: Coding = {
  code: 'BPS',
  display: 'Blood product dispense status message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_BloodProductDispenseStatusAcknowledgementMessage: Coding = {
  code: 'BRP',
  display: 'Blood product dispense status acknowledgement message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_BloodProductTransfusionDispositionAcknowledgementMessage: Coding = {
  code: 'BRT',
  display: 'Blood product transfusion/disposition acknowledgement message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_BloodProductTransfusionDispositionMessage: Coding = {
  code: 'BTS',
  display: 'Blood product transfusion/disposition message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_CollaborativeCareFetch: Coding = {
  code: 'CCF',
  display: 'Collaborative Care Fetch',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_CollaborativeCareInformation: Coding = {
  code: 'CCI',
  display: 'Collaborative Care Information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_CollaborativeCareMessage: Coding = {
  code: 'CCM',
  display: 'Collaborative Care Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_CollaborativeCareReferral: Coding = {
  code: 'CCQ',
  display: 'Collaborative Care Referral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_CollaborativeCareReferral_2: Coding = {
  code: 'CCU',
  display: 'Collaborative Care Referral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_CollaborativeCareReferral_3: Coding = {
  code: 'CQU',
  display: 'Collaborative Care Referral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ClinicalStudyRegistrationMessage: Coding = {
  code: 'CRM',
  display: 'Clinical study registration message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_UnsolicitedStudyDataMessage: Coding = {
  code: 'CSU',
  display: 'Unsolicited study data message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_CreateDonorRecord: Coding = {
  code: 'DBC',
  display: 'Create Donor Record',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_UpdateDonorRecord: Coding = {
  code: 'DBU',
  display: 'Update Donor Record',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_DonorEligibility: Coding = {
  code: 'DEL',
  display: 'Donor Eligibility',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_DonorEligibilityObservation: Coding = {
  code: 'DEO',
  display: 'Donor Eligibility Observation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_DonorEligibilityRequest: Coding = {
  code: 'DER',
  display: 'Donor Eligibility Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_DetailFinancialTransactions: Coding = {
  code: 'DFT',
  display: 'Detail financial transactions',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_DocumentResponse: Coding = {
  code: 'DOC',
  display: 'Document response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_DonationProcedure: Coding = {
  code: 'DPR',
  display: 'Donation Procedure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_DonorRequestToCollect: Coding = {
  code: 'DRC',
  display: 'Donor Request to Collect',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_DisplayResponse: Coding = {
  code: 'DSR',
  display: 'Display response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_AutomatedEquipmentCommandMessage: Coding = {
  code: 'EAC',
  display: 'Automated equipment command message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_AutomatedEquipmentNotificationMessage: Coding = {
  code: 'EAN',
  display: 'Automated equipment notification message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_AutomatedEquipmentResponseMessage: Coding = {
  code: 'EAR',
  display: 'Automated equipment response message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_HealthCareInvoice: Coding = {
  code: 'EHC',
  display: 'Health Care Invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_AutomatedEquipmentStatusUpdateAcknowledgmentMessage: Coding = {
  code: 'ESR',
  display: 'Automated equipment status update acknowledgment message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_AutomatedEquipmentStatusUpdateMessage: Coding = {
  code: 'ESU',
  display: 'Automated equipment status update message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_AutomatedEquipmentInventoryRequestMessage: Coding = {
  code: 'INR',
  display: 'Automated equipment inventory request message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_AutomatedEquipmentInventoryUpdateMessage: Coding = {
  code: 'INU',
  display: 'Automated equipment inventory update message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_AutomatedEquipmentLogServiceRequestMessage: Coding = {
  code: 'LSR',
  display: 'Automated equipment log/service request message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_AutomatedEquipmentLogServiceUpdateMessage: Coding = {
  code: 'LSU',
  display: 'Automated equipment log/service update message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_MedicalDocumentManagement: Coding = {
  code: 'MDM',
  display: 'Medical document management',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_MasterFilesDelayedApplicationAcknowledgment: Coding = {
  code: 'MFD',
  display: 'Master files delayed application acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_MasterFilesApplicationAcknowledgment: Coding = {
  code: 'MFK',
  display: 'Master files application acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_MasterFilesNotification: Coding = {
  code: 'MFN',
  display: 'Master files notification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_MasterFilesQuery: Coding = {
  code: 'MFQ',
  display: 'Master files query',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_MasterFilesResponse: Coding = {
  code: 'MFR',
  display: 'Master files response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ApplicationManagementDataMessage: Coding = {
  code: 'NMD',
  display: 'Application management data message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ApplicationManagementQueryMessage: Coding = {
  code: 'NMQ',
  display: 'Application management query message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ApplicationManagementResponseMessage: Coding = {
  code: 'NMR',
  display: 'Application management response message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_BloodProductOrderMessage: Coding = {
  code: 'OMB',
  display: 'Blood product order message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_DietaryOrder: Coding = {
  code: 'OMD',
  display: 'Dietary order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_GeneralClinicalOrderMessage: Coding = {
  code: 'OMG',
  display: 'General clinical order message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ImagingOrder: Coding = {
  code: 'OMI',
  display: 'Imaging order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_LaboratoryOrderMessage: Coding = {
  code: 'OML',
  display: 'Laboratory order message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_NonStockRequisitionOrderMessage: Coding = {
  code: 'OMN',
  display: 'Non-stock requisition order message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentOrderMessage: Coding = {
  code: 'OMP',
  display: 'Pharmacy/treatment order message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_GeneralOrderMessageWithDocumentPayload: Coding = {
  code: 'OMQ',
  display: 'General order message with document payload',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_StockRequisitionOrderMessage: Coding = {
  code: 'OMS',
  display: 'Stock requisition order message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PopulationLocationBasedLaboratoryOrderMessage: Coding = {
  code: 'OPL',
  display: 'Population/Location-Based Laboratory Order Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PopulationLocationBasedLaboratoryOrderAcknowledgmentMessage: Coding = {
  code: 'OPR',
  display: 'Population/Location-Based Laboratory Order Acknowledgment Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_UnsolicitedPopulationLocationBasedLaboratoryObservationMessage: Coding = {
  code: 'OPU',
  display: 'Unsolicited Population/Location-Based Laboratory Observation Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ObservationReportAcknowledgment: Coding = {
  code: 'ORA',
  display: 'Observation Report Acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_BloodProductOrderAcknowledgementMessage: Coding = {
  code: 'ORB',
  display: 'Blood product order acknowledgement message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_DietaryOrderAcknowledgmentMessage: Coding = {
  code: 'ORD',
  display: 'Dietary order acknowledgment message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_QueryForResultsOfObservation: Coding = {
  code: 'ORF',
  display: 'Query for results of observation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_GeneralClinicalOrderAcknowledgmentMessage: Coding = {
  code: 'ORG',
  display: 'General clinical order acknowledgment message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ImagingOrderAcknowledgementMessage: Coding = {
  code: 'ORI',
  display: 'Imaging order acknowledgement message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_LaboratoryAcknowledgmentMessageUnsolicited: Coding = {
  code: 'ORL',
  display: 'Laboratory acknowledgment message (unsolicited)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentOrderMessage_2: Coding = {
  code: 'ORM',
  display: 'Pharmacy/treatment order message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_NonStockRequisitionGeneralOrderAcknowledgmentMessage: Coding = {
  code: 'ORN',
  display: 'Non-stock requisition - General order acknowledgment message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentOrderAcknowledgmentMessage: Coding = {
  code: 'ORP',
  display: 'Pharmacy/treatment order acknowledgment message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_GeneralOrderResponseMessageResponseToAnyORM: Coding = {
  code: 'ORR',
  display: 'General order response message response to any ORM',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_StockRequisitionOrderAcknowledgmentMessage: Coding = {
  code: 'ORS',
  display: 'Stock requisition - Order acknowledgment message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_UnsolicitedTransmissionOfAnObservationMessage: Coding = {
  code: 'ORU',
  display: 'Unsolicited transmission of an observation message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_GeneralOrderMessageWithDocumentPayloadAcknowledgement: Coding = {
  code: 'ORX',
  display: 'General Order Message with Document Payload Acknowledgement',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_SpecimenShipmentMessage: Coding = {
  code: 'OSM',
  display: 'Specimen Shipment Message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_QueryResponseForOrderStatus: Coding = {
  code: 'OSQ',
  display: 'Query response for order status',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_QueryResponseForOrderStatus_2: Coding = {
  code: 'OSR',
  display: 'Query response for order status',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_OrderStatusUpdate: Coding = {
  code: 'OSU',
  display: 'Order status update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_UnsolicitedLaboratoryObservationMessage: Coding = {
  code: 'OUL',
  display: 'Unsolicited laboratory observation message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ProductExperienceMessage: Coding = {
  code: 'PEX',
  display: 'Product experience message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PatientGoalMessage: Coding = {
  code: 'PGL',
  display: 'Patient goal message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PatientInsuranceInformation: Coding = {
  code: 'PIN',
  display: 'Patient insurance information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_AddPersonnelRecord: Coding = {
  code: 'PMU',
  display: 'Add personnel record',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PatientPathwayMessageGoalOriented: Coding = {
  code: 'PPG',
  display: 'Patient pathway message (goal-oriented)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PatientPathwayMessageProblemOriented: Coding = {
  code: 'PPP',
  display: 'Patient pathway message (problem-oriented)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PatientProblemMessage: Coding = {
  code: 'PPR',
  display: 'Patient problem message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_QueryByParameter: Coding = {
  code: 'QBP',
  display: 'Query by parameter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_QueryGeneralAcknowledgment: Coding = {
  code: 'QCK',
  display: 'Query general acknowledgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_CancelQuery: Coding = {
  code: 'QCN',
  display: 'Cancel query',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_QueryOriginalMode: Coding = {
  code: 'QRY',
  display: 'Query, original mode',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_CreateSubscription: Coding = {
  code: 'QSB',
  display: 'Create subscription',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_CancelSubscriptionAcknowledgeMessage: Coding = {
  code: 'QSX',
  display: 'Cancel subscription/acknowledge message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_QueryForPreviousEvents: Coding = {
  code: 'QVR',
  display: 'Query for previous events',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentAdministrationInformation: Coding = {
  code: 'RAR',
  display: 'Pharmacy/treatment administration information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentAdministrationMessage: Coding = {
  code: 'RAS',
  display: 'Pharmacy/treatment administration message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ReturnClinicalInformation: Coding = {
  code: 'RCI',
  display: 'Return clinical information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ReturnClinicalList: Coding = {
  code: 'RCL',
  display: 'Return clinical list',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentEncodedOrderMessage: Coding = {
  code: 'RDE',
  display: 'Pharmacy/treatment encoded order message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentDispenseInformation: Coding = {
  code: 'RDR',
  display: 'Pharmacy/treatment dispense information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentDispenseMessage: Coding = {
  code: 'RDS',
  display: 'Pharmacy/treatment dispense message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_DisplayBasedResponse: Coding = {
  code: 'RDY',
  display: 'Display based response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PatientReferral: Coding = {
  code: 'REF',
  display: 'Patient referral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentEncodedOrderInformation: Coding = {
  code: 'RER',
  display: 'Pharmacy/treatment encoded order information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentDoseInformation: Coding = {
  code: 'RGR',
  display: 'Pharmacy/treatment dose information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentGiveMessage: Coding = {
  code: 'RGV',
  display: 'Pharmacy/treatment give message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentOrderResponse: Coding = {
  code: 'ROR',
  display: 'Pharmacy/treatment order response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ReturnPatientAuthorization: Coding = {
  code: 'RPA',
  display: 'Return patient authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ReturnPatientInformation: Coding = {
  code: 'RPI',
  display: 'Return patient information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ReturnPatientDisplayList: Coding = {
  code: 'RPL',
  display: 'Return patient display list',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ReturnPatientList: Coding = {
  code: 'RPR',
  display: 'Return patient list',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_RequestPatientAuthorization: Coding = {
  code: 'RQA',
  display: 'Request patient authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_RequestClinicalInformation: Coding = {
  code: 'RQC',
  display: 'Request clinical information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_RequestPatientInformation: Coding = {
  code: 'RQI',
  display: 'Request patient information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_RequestPatientDemographics: Coding = {
  code: 'RQP',
  display: 'Request patient demographics',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentAdministrationAcknowledgmentMessage: Coding = {
  code: 'RRA',
  display: 'Pharmacy/treatment administration acknowledgment message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentDispenseAcknowledgmentMessage: Coding = {
  code: 'RRD',
  display: 'Pharmacy/treatment dispense acknowledgment message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentEncodedOrderAcknowledgmentMessage: Coding = {
  code: 'RRE',
  display: 'Pharmacy/treatment encoded order acknowledgment message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_PharmacyTreatmentGiveAcknowledgmentMessage: Coding = {
  code: 'RRG',
  display: 'Pharmacy/treatment give acknowledgment message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ReturnReferralInformation: Coding = {
  code: 'RRI',
  display: 'Return referral information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_SegmentPatternResponse: Coding = {
  code: 'RSP',
  display: 'Segment pattern response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_TabularResponse: Coding = {
  code: 'RTB',
  display: 'Tabular response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_NotificationOfAntiMicrobialDeviceCycleData: Coding = {
  code: 'SCN',
  display: 'Notification of Anti-Microbial Device Cycle Data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_NotificationOfAntiMicrobialDeviceData: Coding = {
  code: 'SDN',
  display: 'Notification of Anti-Microbial Device Data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_SterilizationAntiMicrobialDeviceDataRequest: Coding = {
  code: 'SDR',
  display: 'Sterilization anti-microbial device data request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ScheduleInformationUnsolicited: Coding = {
  code: 'SIU',
  display: 'Schedule information unsolicited',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_NotificationOfNewSterilizationLot: Coding = {
  code: 'SLN',
  display: 'Notification of New Sterilization Lot',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_SterilizationLotRequest: Coding = {
  code: 'SLR',
  display: 'Sterilization lot request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_SterilizationAntiMicrobialDeviceCycleDataRequest: Coding = {
  code: 'SMD',
  display: 'Sterilization anti-microbial device cycle data request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ScheduleQueryMessage: Coding = {
  code: 'SQM',
  display: 'Schedule query message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ScheduleQueryResponse: Coding = {
  code: 'SQR',
  display: 'Schedule query response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ScheduleRequestMessage: Coding = {
  code: 'SRM',
  display: 'Schedule request message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ScheduledRequestResponse: Coding = {
  code: 'SRR',
  display: 'Scheduled request response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_SpecimenStatusRequestMessage: Coding = {
  code: 'SSR',
  display: 'Specimen status request message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_SpecimenStatusUpdateMessage: Coding = {
  code: 'SSU',
  display: 'Specimen status update message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_NotificationOfSterilizationConfiguration: Coding = {
  code: 'STC',
  display: 'Notification of Sterilization Configuration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_SterilizationItemRequest: Coding = {
  code: 'STI',
  display: 'Sterilization item request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_SummaryProductExperienceReport: Coding = {
  code: 'SUR',
  display: 'Summary product experience report',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_TabularDataResponse: Coding = {
  code: 'TBR',
  display: 'Tabular data response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_AutomatedEquipmentTestCodeSettingsRequestMessage: Coding = {
  code: 'TCR',
  display: 'Automated equipment test code settings request message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_AutomatedEquipmentTestCodeSettingsUpdateMessage: Coding = {
  code: 'TCU',
  display: 'Automated equipment test code settings update message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_UnsolicitedDisplayUpdateMessage: Coding = {
  code: 'UDM',
  display: 'Unsolicited display update message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_QueryForVaccinationRecord: Coding = {
  code: 'VXQ',
  display: 'Query for vaccination record',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_VaccinationRecordResponse: Coding = {
  code: 'VXR',
  display: 'Vaccination record response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_UnsolicitedVaccinationRecordUpdate: Coding = {
  code: 'VXU',
  display: 'Unsolicited vaccination record update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
const v2_0076_ResponseForVaccinationQueryWithMultiplePIDMatches: Coding = {
  code: 'VXX',
  display: 'Response for vaccination query with multiple PID matches',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0076',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0076 ( MESSAGE TYPE)
 */
export const V20076 = {
  GeneralAcknowledgmentMessage: v2_0076_GeneralAcknowledgmentMessage,
  ADTResponse: v2_0076_ADTResponse,
  ADTMessage: v2_0076_ADTMessage,
  AddChangeBillingAccount: v2_0076_AddChangeBillingAccount,
  BloodProductDispenseStatusMessage: v2_0076_BloodProductDispenseStatusMessage,
  BloodProductDispenseStatusAcknowledgementMessage: v2_0076_BloodProductDispenseStatusAcknowledgementMessage,
  BloodProductTransfusionDispositionAcknowledgementMessage: v2_0076_BloodProductTransfusionDispositionAcknowledgementMessage,
  BloodProductTransfusionDispositionMessage: v2_0076_BloodProductTransfusionDispositionMessage,
  CollaborativeCareFetch: v2_0076_CollaborativeCareFetch,
  CollaborativeCareInformation: v2_0076_CollaborativeCareInformation,
  CollaborativeCareMessage: v2_0076_CollaborativeCareMessage,
  CollaborativeCareReferral: v2_0076_CollaborativeCareReferral,
  CollaborativeCareReferral_2: v2_0076_CollaborativeCareReferral_2,
  CollaborativeCareReferral_3: v2_0076_CollaborativeCareReferral_3,
  ClinicalStudyRegistrationMessage: v2_0076_ClinicalStudyRegistrationMessage,
  UnsolicitedStudyDataMessage: v2_0076_UnsolicitedStudyDataMessage,
  CreateDonorRecord: v2_0076_CreateDonorRecord,
  UpdateDonorRecord: v2_0076_UpdateDonorRecord,
  DonorEligibility: v2_0076_DonorEligibility,
  DonorEligibilityObservation: v2_0076_DonorEligibilityObservation,
  DonorEligibilityRequest: v2_0076_DonorEligibilityRequest,
  DetailFinancialTransactions: v2_0076_DetailFinancialTransactions,
  DocumentResponse: v2_0076_DocumentResponse,
  DonationProcedure: v2_0076_DonationProcedure,
  DonorRequestToCollect: v2_0076_DonorRequestToCollect,
  DisplayResponse: v2_0076_DisplayResponse,
  AutomatedEquipmentCommandMessage: v2_0076_AutomatedEquipmentCommandMessage,
  AutomatedEquipmentNotificationMessage: v2_0076_AutomatedEquipmentNotificationMessage,
  AutomatedEquipmentResponseMessage: v2_0076_AutomatedEquipmentResponseMessage,
  HealthCareInvoice: v2_0076_HealthCareInvoice,
  AutomatedEquipmentStatusUpdateAcknowledgmentMessage: v2_0076_AutomatedEquipmentStatusUpdateAcknowledgmentMessage,
  AutomatedEquipmentStatusUpdateMessage: v2_0076_AutomatedEquipmentStatusUpdateMessage,
  AutomatedEquipmentInventoryRequestMessage: v2_0076_AutomatedEquipmentInventoryRequestMessage,
  AutomatedEquipmentInventoryUpdateMessage: v2_0076_AutomatedEquipmentInventoryUpdateMessage,
  AutomatedEquipmentLogServiceRequestMessage: v2_0076_AutomatedEquipmentLogServiceRequestMessage,
  AutomatedEquipmentLogServiceUpdateMessage: v2_0076_AutomatedEquipmentLogServiceUpdateMessage,
  MedicalDocumentManagement: v2_0076_MedicalDocumentManagement,
  MasterFilesDelayedApplicationAcknowledgment: v2_0076_MasterFilesDelayedApplicationAcknowledgment,
  MasterFilesApplicationAcknowledgment: v2_0076_MasterFilesApplicationAcknowledgment,
  MasterFilesNotification: v2_0076_MasterFilesNotification,
  MasterFilesQuery: v2_0076_MasterFilesQuery,
  MasterFilesResponse: v2_0076_MasterFilesResponse,
  ApplicationManagementDataMessage: v2_0076_ApplicationManagementDataMessage,
  ApplicationManagementQueryMessage: v2_0076_ApplicationManagementQueryMessage,
  ApplicationManagementResponseMessage: v2_0076_ApplicationManagementResponseMessage,
  BloodProductOrderMessage: v2_0076_BloodProductOrderMessage,
  DietaryOrder: v2_0076_DietaryOrder,
  GeneralClinicalOrderMessage: v2_0076_GeneralClinicalOrderMessage,
  ImagingOrder: v2_0076_ImagingOrder,
  LaboratoryOrderMessage: v2_0076_LaboratoryOrderMessage,
  NonStockRequisitionOrderMessage: v2_0076_NonStockRequisitionOrderMessage,
  PharmacyTreatmentOrderMessage: v2_0076_PharmacyTreatmentOrderMessage,
  GeneralOrderMessageWithDocumentPayload: v2_0076_GeneralOrderMessageWithDocumentPayload,
  StockRequisitionOrderMessage: v2_0076_StockRequisitionOrderMessage,
  PopulationLocationBasedLaboratoryOrderMessage: v2_0076_PopulationLocationBasedLaboratoryOrderMessage,
  PopulationLocationBasedLaboratoryOrderAcknowledgmentMessage: v2_0076_PopulationLocationBasedLaboratoryOrderAcknowledgmentMessage,
  UnsolicitedPopulationLocationBasedLaboratoryObservationMessage: v2_0076_UnsolicitedPopulationLocationBasedLaboratoryObservationMessage,
  ObservationReportAcknowledgment: v2_0076_ObservationReportAcknowledgment,
  BloodProductOrderAcknowledgementMessage: v2_0076_BloodProductOrderAcknowledgementMessage,
  DietaryOrderAcknowledgmentMessage: v2_0076_DietaryOrderAcknowledgmentMessage,
  QueryForResultsOfObservation: v2_0076_QueryForResultsOfObservation,
  GeneralClinicalOrderAcknowledgmentMessage: v2_0076_GeneralClinicalOrderAcknowledgmentMessage,
  ImagingOrderAcknowledgementMessage: v2_0076_ImagingOrderAcknowledgementMessage,
  LaboratoryAcknowledgmentMessageUnsolicited: v2_0076_LaboratoryAcknowledgmentMessageUnsolicited,
  PharmacyTreatmentOrderMessage_2: v2_0076_PharmacyTreatmentOrderMessage_2,
  NonStockRequisitionGeneralOrderAcknowledgmentMessage: v2_0076_NonStockRequisitionGeneralOrderAcknowledgmentMessage,
  PharmacyTreatmentOrderAcknowledgmentMessage: v2_0076_PharmacyTreatmentOrderAcknowledgmentMessage,
  GeneralOrderResponseMessageResponseToAnyORM: v2_0076_GeneralOrderResponseMessageResponseToAnyORM,
  StockRequisitionOrderAcknowledgmentMessage: v2_0076_StockRequisitionOrderAcknowledgmentMessage,
  UnsolicitedTransmissionOfAnObservationMessage: v2_0076_UnsolicitedTransmissionOfAnObservationMessage,
  GeneralOrderMessageWithDocumentPayloadAcknowledgement: v2_0076_GeneralOrderMessageWithDocumentPayloadAcknowledgement,
  SpecimenShipmentMessage: v2_0076_SpecimenShipmentMessage,
  QueryResponseForOrderStatus: v2_0076_QueryResponseForOrderStatus,
  QueryResponseForOrderStatus_2: v2_0076_QueryResponseForOrderStatus_2,
  OrderStatusUpdate: v2_0076_OrderStatusUpdate,
  UnsolicitedLaboratoryObservationMessage: v2_0076_UnsolicitedLaboratoryObservationMessage,
  ProductExperienceMessage: v2_0076_ProductExperienceMessage,
  PatientGoalMessage: v2_0076_PatientGoalMessage,
  PatientInsuranceInformation: v2_0076_PatientInsuranceInformation,
  AddPersonnelRecord: v2_0076_AddPersonnelRecord,
  PatientPathwayMessageGoalOriented: v2_0076_PatientPathwayMessageGoalOriented,
  PatientPathwayMessageProblemOriented: v2_0076_PatientPathwayMessageProblemOriented,
  PatientProblemMessage: v2_0076_PatientProblemMessage,
  QueryByParameter: v2_0076_QueryByParameter,
  QueryGeneralAcknowledgment: v2_0076_QueryGeneralAcknowledgment,
  CancelQuery: v2_0076_CancelQuery,
  QueryOriginalMode: v2_0076_QueryOriginalMode,
  CreateSubscription: v2_0076_CreateSubscription,
  CancelSubscriptionAcknowledgeMessage: v2_0076_CancelSubscriptionAcknowledgeMessage,
  QueryForPreviousEvents: v2_0076_QueryForPreviousEvents,
  PharmacyTreatmentAdministrationInformation: v2_0076_PharmacyTreatmentAdministrationInformation,
  PharmacyTreatmentAdministrationMessage: v2_0076_PharmacyTreatmentAdministrationMessage,
  ReturnClinicalInformation: v2_0076_ReturnClinicalInformation,
  ReturnClinicalList: v2_0076_ReturnClinicalList,
  PharmacyTreatmentEncodedOrderMessage: v2_0076_PharmacyTreatmentEncodedOrderMessage,
  PharmacyTreatmentDispenseInformation: v2_0076_PharmacyTreatmentDispenseInformation,
  PharmacyTreatmentDispenseMessage: v2_0076_PharmacyTreatmentDispenseMessage,
  DisplayBasedResponse: v2_0076_DisplayBasedResponse,
  PatientReferral: v2_0076_PatientReferral,
  PharmacyTreatmentEncodedOrderInformation: v2_0076_PharmacyTreatmentEncodedOrderInformation,
  PharmacyTreatmentDoseInformation: v2_0076_PharmacyTreatmentDoseInformation,
  PharmacyTreatmentGiveMessage: v2_0076_PharmacyTreatmentGiveMessage,
  PharmacyTreatmentOrderResponse: v2_0076_PharmacyTreatmentOrderResponse,
  ReturnPatientAuthorization: v2_0076_ReturnPatientAuthorization,
  ReturnPatientInformation: v2_0076_ReturnPatientInformation,
  ReturnPatientDisplayList: v2_0076_ReturnPatientDisplayList,
  ReturnPatientList: v2_0076_ReturnPatientList,
  RequestPatientAuthorization: v2_0076_RequestPatientAuthorization,
  RequestClinicalInformation: v2_0076_RequestClinicalInformation,
  RequestPatientInformation: v2_0076_RequestPatientInformation,
  RequestPatientDemographics: v2_0076_RequestPatientDemographics,
  PharmacyTreatmentAdministrationAcknowledgmentMessage: v2_0076_PharmacyTreatmentAdministrationAcknowledgmentMessage,
  PharmacyTreatmentDispenseAcknowledgmentMessage: v2_0076_PharmacyTreatmentDispenseAcknowledgmentMessage,
  PharmacyTreatmentEncodedOrderAcknowledgmentMessage: v2_0076_PharmacyTreatmentEncodedOrderAcknowledgmentMessage,
  PharmacyTreatmentGiveAcknowledgmentMessage: v2_0076_PharmacyTreatmentGiveAcknowledgmentMessage,
  ReturnReferralInformation: v2_0076_ReturnReferralInformation,
  SegmentPatternResponse: v2_0076_SegmentPatternResponse,
  TabularResponse: v2_0076_TabularResponse,
  NotificationOfAntiMicrobialDeviceCycleData: v2_0076_NotificationOfAntiMicrobialDeviceCycleData,
  NotificationOfAntiMicrobialDeviceData: v2_0076_NotificationOfAntiMicrobialDeviceData,
  SterilizationAntiMicrobialDeviceDataRequest: v2_0076_SterilizationAntiMicrobialDeviceDataRequest,
  ScheduleInformationUnsolicited: v2_0076_ScheduleInformationUnsolicited,
  NotificationOfNewSterilizationLot: v2_0076_NotificationOfNewSterilizationLot,
  SterilizationLotRequest: v2_0076_SterilizationLotRequest,
  SterilizationAntiMicrobialDeviceCycleDataRequest: v2_0076_SterilizationAntiMicrobialDeviceCycleDataRequest,
  ScheduleQueryMessage: v2_0076_ScheduleQueryMessage,
  ScheduleQueryResponse: v2_0076_ScheduleQueryResponse,
  ScheduleRequestMessage: v2_0076_ScheduleRequestMessage,
  ScheduledRequestResponse: v2_0076_ScheduledRequestResponse,
  SpecimenStatusRequestMessage: v2_0076_SpecimenStatusRequestMessage,
  SpecimenStatusUpdateMessage: v2_0076_SpecimenStatusUpdateMessage,
  NotificationOfSterilizationConfiguration: v2_0076_NotificationOfSterilizationConfiguration,
  SterilizationItemRequest: v2_0076_SterilizationItemRequest,
  SummaryProductExperienceReport: v2_0076_SummaryProductExperienceReport,
  TabularDataResponse: v2_0076_TabularDataResponse,
  AutomatedEquipmentTestCodeSettingsRequestMessage: v2_0076_AutomatedEquipmentTestCodeSettingsRequestMessage,
  AutomatedEquipmentTestCodeSettingsUpdateMessage: v2_0076_AutomatedEquipmentTestCodeSettingsUpdateMessage,
  UnsolicitedDisplayUpdateMessage: v2_0076_UnsolicitedDisplayUpdateMessage,
  QueryForVaccinationRecord: v2_0076_QueryForVaccinationRecord,
  VaccinationRecordResponse: v2_0076_VaccinationRecordResponse,
  UnsolicitedVaccinationRecordUpdate: v2_0076_UnsolicitedVaccinationRecordUpdate,
  ResponseForVaccinationQueryWithMultiplePIDMatches: v2_0076_ResponseForVaccinationQueryWithMultiplePIDMatches,
};
const v2_0080_AnAgeBasedPopulation: Coding = {
  code: 'A',
  display: 'An age-based population',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0080',
};
const v2_0080_Breed: Coding = {
  code: 'B',
  display: 'Breed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0080',
};
const v2_0080_NoneGenericNormalRange: Coding = {
  code: 'N',
  display: 'None - generic normal range',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0080',
};
const v2_0080_ARaceBasedPopulation: Coding = {
  code: 'R',
  display: 'A race-based population',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0080',
};
const v2_0080_ASexBasedPopulation: Coding = {
  code: 'S',
  display: 'A sex-based population',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0080',
};
const v2_0080_Species: Coding = {
  code: 'SP',
  display: 'Species',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0080',
};
const v2_0080_Strain: Coding = {
  code: 'ST',
  display: 'Strain',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0080',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0080 ( NATURE OF ABNORMAL TESTING)
 */
export const V20080 = {
  AnAgeBasedPopulation: v2_0080_AnAgeBasedPopulation,
  Breed: v2_0080_Breed,
  NoneGenericNormalRange: v2_0080_NoneGenericNormalRange,
  ARaceBasedPopulation: v2_0080_ARaceBasedPopulation,
  ASexBasedPopulation: v2_0080_ASexBasedPopulation,
  Species: v2_0080_Species,
  Strain: v2_0080_Strain,
};
const v2_0083_OutlierCost: Coding = {
  code: 'C',
  display: 'Outlier cost',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0083',
};
const v2_0083_OutlierDays: Coding = {
  code: 'D',
  display: 'Outlier days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0083',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0083 ( Outlier Type)
 */
export const V20083 = {
  OutlierCost: v2_0083_OutlierCost,
  OutlierDays: v2_0083_OutlierDays,
};
const v2_0085_AmendedBasedOnAdjustmentsProvidedByThePlacerPhysicianRegardingPatientDemographicsSuchAsAgeAndOrGenderOrOtherPatientSpecificInformation: Coding = {
  code: 'A',
  display:
    'Amended based on adjustments provided by the Placer (Physician) regarding patient demographics (such as age and/or gender or other patient specific information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_AppendedReportFinalResultsReviewedAndFurtherInformationProvidedForClarityWithoutChangeToTheOriginalResultValues: Coding = {
  code: 'B',
  display:
    'Appended Report - Final results reviewed and further information provided for clarity without change to the original result values.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_RecordComingOverIsACorrectionAndThusReplacesAFinalResult: Coding = {
  code: 'C',
  display: 'Record coming over is a correction and thus replaces a final result',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_DeletesTheOBXRecord: Coding = {
  code: 'D',
  display: 'Deletes the OBX record',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_FinalResults: Coding = {
  code: 'F',
  display: 'Final results',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_SpecimenInLabResultsPending: Coding = {
  code: 'I',
  display: 'Specimen in lab; results pending',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_NotAskedUsedToAffirmativelyDocumentThatTheObservationIdentifiedInTheOBXWasNotSoughtWhenTheUniversalServiceIDInOBR4ImpliesThatItWouldBeSought: Coding = {
  code: 'N',
  display:
    'Not asked; used to affirmatively document that the observation identified in the OBX was not sought when the universal service ID in OBR-4 implies that it would be sought.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_OrderDetailDescriptionOnlyNoResult: Coding = {
  code: 'O',
  display: 'Order detail description only (no result)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_PreliminaryResults: Coding = {
  code: 'P',
  display: 'Preliminary results',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_ResultsEnteredNotVerified: Coding = {
  code: 'R',
  display: 'Results entered -- not verified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_PartialResultsDeprecatedRetainedOnlyForBackwardCompatibilityAsOfV26: Coding = {
  code: 'S',
  display: 'Partial results.   Deprecated. Retained only for backward compatibility as of V2.6.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_ResultsStatusChangeToFinalWithoutRetransmittingResultsAlreadySentAsQuotePreliminaryQuoteEGRadiologyChangesStatusFromPreliminaryToFinal: Coding = {
  code: 'U',
  display:
    "Results status change to final without retransmitting results already sent as 'preliminary.'  E.g., radiology changes status from preliminary to final",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_VerifiedFinalResultsReviewedAndConfirmedToBeCorrectNoChangeToResultValueNormalRangeOrAbnormalFlag: Coding = {
  code: 'V',
  display: 'Verified - Final results reviewed and confirmed to be correct, no change to result value, normal range or abnormal flag',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_PostOriginalAsWrongEGTransmittedForWrongPatient: Coding = {
  code: 'W',
  display: 'Post original as wrong, e.g., transmitted for wrong patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
const v2_0085_ResultsCannotBeObtainedForThisObservation: Coding = {
  code: 'X',
  display: 'Results cannot be obtained for this observation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0085',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0085 ( OBSERVATION RESULT STATUS)
 */
export const V20085 = {
  AmendedBasedOnAdjustmentsProvidedByThePlacerPhysicianRegardingPatientDemographicsSuchAsAgeAndOrGenderOrOtherPatientSpecificInformation: v2_0085_AmendedBasedOnAdjustmentsProvidedByThePlacerPhysicianRegardingPatientDemographicsSuchAsAgeAndOrGenderOrOtherPatientSpecificInformation,
  AppendedReportFinalResultsReviewedAndFurtherInformationProvidedForClarityWithoutChangeToTheOriginalResultValues: v2_0085_AppendedReportFinalResultsReviewedAndFurtherInformationProvidedForClarityWithoutChangeToTheOriginalResultValues,
  RecordComingOverIsACorrectionAndThusReplacesAFinalResult: v2_0085_RecordComingOverIsACorrectionAndThusReplacesAFinalResult,
  DeletesTheOBXRecord: v2_0085_DeletesTheOBXRecord,
  FinalResults: v2_0085_FinalResults,
  SpecimenInLabResultsPending: v2_0085_SpecimenInLabResultsPending,
  NotAskedUsedToAffirmativelyDocumentThatTheObservationIdentifiedInTheOBXWasNotSoughtWhenTheUniversalServiceIDInOBR4ImpliesThatItWouldBeSought: v2_0085_NotAskedUsedToAffirmativelyDocumentThatTheObservationIdentifiedInTheOBXWasNotSoughtWhenTheUniversalServiceIDInOBR4ImpliesThatItWouldBeSought,
  OrderDetailDescriptionOnlyNoResult: v2_0085_OrderDetailDescriptionOnlyNoResult,
  PreliminaryResults: v2_0085_PreliminaryResults,
  ResultsEnteredNotVerified: v2_0085_ResultsEnteredNotVerified,
  PartialResultsDeprecatedRetainedOnlyForBackwardCompatibilityAsOfV26: v2_0085_PartialResultsDeprecatedRetainedOnlyForBackwardCompatibilityAsOfV26,
  ResultsStatusChangeToFinalWithoutRetransmittingResultsAlreadySentAsQuotePreliminaryQuoteEGRadiologyChangesStatusFromPreliminaryToFinal: v2_0085_ResultsStatusChangeToFinalWithoutRetransmittingResultsAlreadySentAsQuotePreliminaryQuoteEGRadiologyChangesStatusFromPreliminaryToFinal,
  VerifiedFinalResultsReviewedAndConfirmedToBeCorrectNoChangeToResultValueNormalRangeOrAbnormalFlag: v2_0085_VerifiedFinalResultsReviewedAndConfirmedToBeCorrectNoChangeToResultValueNormalRangeOrAbnormalFlag,
  PostOriginalAsWrongEGTransmittedForWrongPatient: v2_0085_PostOriginalAsWrongEGTransmittedForWrongPatient,
  ResultsCannotBeObtainedForThisObservation: v2_0085_ResultsCannotBeObtainedForThisObservation,
};
const v2_0091_Deferred: Coding = {
  code: 'D',
  display: 'Deferred',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0091',
};
const v2_0091_Immediate: Coding = {
  code: 'I',
  display: 'Immediate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0091',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0091 ( QUERY PRIORITY)
 */
export const V20091 = {
  Deferred: v2_0091_Deferred,
  Immediate: v2_0091_Immediate,
};
const v2_0092_ReAdmission: Coding = {
  code: 'R',
  display: 'Re-admission',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0092',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0092 ( RE-ADMISSION INDICATOR)
 */
export const V20092 = {
  ReAdmission: v2_0092_ReAdmission,
};
const v2_0098_Maternity: Coding = {
  code: 'M',
  display: 'Maternity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0098',
};
const v2_0098_Standard: Coding = {
  code: 'S',
  display: 'Standard',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0098',
};
const v2_0098_Unified: Coding = {
  code: 'U',
  display: 'Unified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0098',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0098 ( Type of Agreement)
 */
export const V20098 = {
  Maternity: v2_0098_Maternity,
  Standard: v2_0098_Standard,
  Unified: v2_0098_Unified,
};
const v2_0100_OnDischarge: Coding = {
  code: 'D',
  display: 'On discharge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0100',
};
const v2_0100_OnReceiptOfOrder: Coding = {
  code: 'O',
  display: 'On receipt of order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0100',
};
const v2_0100_AtTimeServiceIsCompleted: Coding = {
  code: 'R',
  display: 'At time service is completed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0100',
};
const v2_0100_AtTimeServiceIsStarted: Coding = {
  code: 'S',
  display: 'At time service is started',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0100',
};
const v2_0100_AtADesignatedDateTime: Coding = {
  code: 'T',
  display: 'At a designated date/time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0100',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0100 ( WHEN TO CHARGE)
 */
export const V20100 = {
  OnDischarge: v2_0100_OnDischarge,
  OnReceiptOfOrder: v2_0100_OnReceiptOfOrder,
  AtTimeServiceIsCompleted: v2_0100_AtTimeServiceIsCompleted,
  AtTimeServiceIsStarted: v2_0100_AtTimeServiceIsStarted,
  AtADesignatedDateTime: v2_0100_AtADesignatedDateTime,
};
const v2_0103_Debugging: Coding = {
  code: 'D',
  display: 'Debugging',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0103',
};
const v2_0103_NonProductionTesting: Coding = {
  code: 'N',
  display: 'Non-Production Testing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0103',
};
const v2_0103_Production: Coding = {
  code: 'P',
  display: 'Production',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0103',
};
const v2_0103_Training: Coding = {
  code: 'T',
  display: 'Training',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0103',
};
const v2_0103_Validation: Coding = {
  code: 'V',
  display: 'Validation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0103',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0103 ( PROCESSING ID)
 */
export const V20103 = {
  Debugging: v2_0103_Debugging,
  NonProductionTesting: v2_0103_NonProductionTesting,
  Production: v2_0103_Production,
  Training: v2_0103_Training,
  Validation: v2_0103_Validation,
};
const v2_0104_Release20: Coding = {
  code: '2.0',
  display: 'Release 2.0',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Demo20: Coding = {
  code: '2.0D',
  display: 'Demo 2.0',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Release21: Coding = {
  code: '2.1',
  display: 'Release 2.1',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Release22: Coding = {
  code: '2.2',
  display: 'Release 2.2',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Release23: Coding = {
  code: '2.3',
  display: 'Release 2.3',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Release231: Coding = {
  code: '2.3.1',
  display: 'Release 2.3.1',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Release24: Coding = {
  code: '2.4',
  display: 'Release 2.4',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Release25: Coding = {
  code: '2.5',
  display: 'Release 2.5',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Release251: Coding = {
  code: '2.5.1',
  display: 'Release 2.5.1',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Release26: Coding = {
  code: '2.6',
  display: 'Release 2.6',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Release27: Coding = {
  code: '2.7',
  display: 'Release 2.7',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Release271: Coding = {
  code: '2.7.1',
  display: 'Release 2.7.1',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Release28: Coding = {
  code: '2.8',
  display: 'Release 2.8',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Release281: Coding = {
  code: '2.8.1',
  display: 'Release 2.8.1',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Release282: Coding = {
  code: '2.8.2',
  display: 'Release 2.8.2',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
const v2_0104_Draft29: Coding = {
  code: '2.9',
  display: 'Draft 2.9',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0104',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0104 ( VERSION CONTROL TABLE)
 */
export const V20104 = {
  Release20: v2_0104_Release20,
  Demo20: v2_0104_Demo20,
  Release21: v2_0104_Release21,
  Release22: v2_0104_Release22,
  Release23: v2_0104_Release23,
  Release231: v2_0104_Release231,
  Release24: v2_0104_Release24,
  Release25: v2_0104_Release25,
  Release251: v2_0104_Release251,
  Release26: v2_0104_Release26,
  Release27: v2_0104_Release27,
  Release271: v2_0104_Release271,
  Release28: v2_0104_Release28,
  Release281: v2_0104_Release281,
  Release282: v2_0104_Release282,
  Draft29: v2_0104_Draft29,
};
const v2_0105_AncillaryFillerDepartmentIsSourceOfComment: Coding = {
  code: 'L',
  display: 'Ancillary (filler) department is source of comment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0105',
};
const v2_0105_OtherSystemIsSourceOfComment: Coding = {
  code: 'O',
  display: 'Other system is source of comment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0105',
};
const v2_0105_OrdererPlacerIsSourceOfComment: Coding = {
  code: 'P',
  display: 'Orderer (placer) is source of comment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0105',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0105 ( SOURCE OF COMMENT)
 */
export const V20105 = {
  AncillaryFillerDepartmentIsSourceOfComment: v2_0105_AncillaryFillerDepartmentIsSourceOfComment,
  OtherSystemIsSourceOfComment: v2_0105_OtherSystemIsSourceOfComment,
  OrdererPlacerIsSourceOfComment: v2_0105_OrdererPlacerIsSourceOfComment,
};
const v2_0116_Closed: Coding = {
  code: 'C',
  display: 'Closed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0116',
};
const v2_0116_Housekeeping: Coding = {
  code: 'H',
  display: 'Housekeeping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0116',
};
const v2_0116_Isolated: Coding = {
  code: 'I',
  display: 'Isolated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0116',
};
const v2_0116_Contaminated: Coding = {
  code: 'K',
  display: 'Contaminated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0116',
};
const v2_0116_Occupied: Coding = {
  code: 'O',
  display: 'Occupied',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0116',
};
const v2_0116_Unoccupied: Coding = {
  code: 'U',
  display: 'Unoccupied',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0116',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0116 ( BED STATUS)
 */
export const V20116 = {
  Closed: v2_0116_Closed,
  Housekeeping: v2_0116_Housekeeping,
  Isolated: v2_0116_Isolated,
  Contaminated: v2_0116_Contaminated,
  Occupied: v2_0116_Occupied,
  Unoccupied: v2_0116_Unoccupied,
};
const v2_0119_OrderServiceRefillRequestApproval: Coding = {
  code: 'AF',
  display: 'Order/service refill request approval',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_CancelOrderServiceRequest: Coding = {
  code: 'CA',
  display: 'Cancel order/service request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_ChildOrderService: Coding = {
  code: 'CH',
  display: 'Child order/service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_CombinedResult: Coding = {
  code: 'CN',
  display: 'Combined result',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_CancelProcessStep: Coding = {
  code: 'CP',
  display: 'Cancel process step',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_CanceledAsRequested: Coding = {
  code: 'CR',
  display: 'Canceled as requested',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_DiscontinueOrderServiceRequest: Coding = {
  code: 'DC',
  display: 'Discontinue order/service request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_DataErrors: Coding = {
  code: 'DE',
  display: 'Data errors',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_OrderServiceRefillRequestDenied: Coding = {
  code: 'DF',
  display: 'Order/service refill request denied',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_DiscontinuedAsRequested: Coding = {
  code: 'DR',
  display: 'Discontinued as requested',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_OrderServiceRefilledUnsolicited: Coding = {
  code: 'FU',
  display: 'Order/service refilled, unsolicited',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_HoldOrderRequest: Coding = {
  code: 'HD',
  display: 'Hold order request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_OnHoldAsRequested: Coding = {
  code: 'HR',
  display: 'On hold as requested',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_LinkOrderServiceToPatientCareProblemOrGoal: Coding = {
  code: 'LI',
  display: 'Link order/service to patient care problem or goal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_MiscellaneousChargeNotAssociatedWithAnOrder: Coding = {
  code: 'MC',
  display: 'Miscellaneous Charge - not associated with an order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_NumberAssigned: Coding = {
  code: 'NA',
  display: 'Number assigned',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_NotificationReceived: Coding = {
  code: 'NR',
  display: 'Notification Received',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_NewOrderService: Coding = {
  code: 'NW',
  display: 'New order/service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_OrderServiceCanceled: Coding = {
  code: 'OC',
  display: 'Order/service canceled',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_OrderServiceDiscontinued: Coding = {
  code: 'OD',
  display: 'Order/service discontinued',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_OrderServiceReleased: Coding = {
  code: 'OE',
  display: 'Order/service released',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_OrderServiceRefilledAsRequested: Coding = {
  code: 'OF',
  display: 'Order/service refilled as requested',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_OrderServiceHeld: Coding = {
  code: 'OH',
  display: 'Order/service held',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_OrderServiceAcceptedAndOK: Coding = {
  code: 'OK',
  display: 'Order/service accepted & OK',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_NotificationOfOrderForOutsideDispense: Coding = {
  code: 'OP',
  display: 'Notification of order for outside dispense',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_ReleasedAsRequested: Coding = {
  code: 'OR',
  display: 'Released as requested',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_ParentOrderService: Coding = {
  code: 'PA',
  display: 'Parent order/service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_PreviousResultsWithNewOrderService: Coding = {
  code: 'PR',
  display: 'Previous Results with new order/service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_NotificationOfReplacementOrderForOutsideDispense: Coding = {
  code: 'PY',
  display: 'Notification of replacement order for outside dispense',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_RecommendationAccepted: Coding = {
  code: 'RA',
  display: 'Recommendation Accepted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_RecommendedChange: Coding = {
  code: 'RC',
  display: 'Recommended Change',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_RecommendationDeclined: Coding = {
  code: 'RD',
  display: 'Recommendation Declined',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_ObservationsPerformedServiceToFollow: Coding = {
  code: 'RE',
  display: 'Observations/Performed Service to follow',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_RefillOrderServiceRequest: Coding = {
  code: 'RF',
  display: 'Refill order/service request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_ReleasePreviousHold: Coding = {
  code: 'RL',
  display: 'Release previous hold',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_ReplacementOrder: Coding = {
  code: 'RO',
  display: 'Replacement order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_OrderServiceReplaceRequest: Coding = {
  code: 'RP',
  display: 'Order/service replace request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_ReplacedAsRequested: Coding = {
  code: 'RQ',
  display: 'Replaced as requested',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_RequestReceived: Coding = {
  code: 'RR',
  display: 'Request received',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_ReplacedUnsolicited: Coding = {
  code: 'RU',
  display: 'Replaced unsolicited',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_StatusChanged: Coding = {
  code: 'SC',
  display: 'Status changed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_SendOrderServiceNumber: Coding = {
  code: 'SN',
  display: 'Send order/service number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_SupplementedAsRequested: Coding = {
  code: 'SQ',
  display: 'Supplemented as requested',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_ResponseToSendOrderServiceStatusRequest: Coding = {
  code: 'SR',
  display: 'Response to send order/service status request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_SendOrderServiceStatusRequest: Coding = {
  code: 'SS',
  display: 'Send order/service status request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_SupplementThisOrder: Coding = {
  code: 'SU',
  display: 'Supplement this order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_UnableToAcceptOrderService: Coding = {
  code: 'UA',
  display: 'Unable to accept order/service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_UnableToCancel: Coding = {
  code: 'UC',
  display: 'Unable to cancel',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_UnableToDiscontinue: Coding = {
  code: 'UD',
  display: 'Unable to discontinue',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_UnableToRefill: Coding = {
  code: 'UF',
  display: 'Unable to refill',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_UnableToPutOnHold: Coding = {
  code: 'UH',
  display: 'Unable to put on hold',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_UnableToReplace: Coding = {
  code: 'UM',
  display: 'Unable to replace',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_UnlinkOrderServiceFromPatientCareProblemOrGoal: Coding = {
  code: 'UN',
  display: 'Unlink order/service from patient care problem or goal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_UnableToRelease: Coding = {
  code: 'UR',
  display: 'Unable to release',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_UnableToChange: Coding = {
  code: 'UX',
  display: 'Unable to change',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_ChangeOrderServiceRequest: Coding = {
  code: 'XO',
  display: 'Change order/service request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_ChangedAsRequested: Coding = {
  code: 'XR',
  display: 'Changed as requested',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
const v2_0119_OrderServiceChangedUnsol: Coding = {
  code: 'XX',
  display: 'Order/service changed, unsol.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0119',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0119 ( ORDER CONTROL)
 */
export const V20119 = {
  OrderServiceRefillRequestApproval: v2_0119_OrderServiceRefillRequestApproval,
  CancelOrderServiceRequest: v2_0119_CancelOrderServiceRequest,
  ChildOrderService: v2_0119_ChildOrderService,
  CombinedResult: v2_0119_CombinedResult,
  CancelProcessStep: v2_0119_CancelProcessStep,
  CanceledAsRequested: v2_0119_CanceledAsRequested,
  DiscontinueOrderServiceRequest: v2_0119_DiscontinueOrderServiceRequest,
  DataErrors: v2_0119_DataErrors,
  OrderServiceRefillRequestDenied: v2_0119_OrderServiceRefillRequestDenied,
  DiscontinuedAsRequested: v2_0119_DiscontinuedAsRequested,
  OrderServiceRefilledUnsolicited: v2_0119_OrderServiceRefilledUnsolicited,
  HoldOrderRequest: v2_0119_HoldOrderRequest,
  OnHoldAsRequested: v2_0119_OnHoldAsRequested,
  LinkOrderServiceToPatientCareProblemOrGoal: v2_0119_LinkOrderServiceToPatientCareProblemOrGoal,
  MiscellaneousChargeNotAssociatedWithAnOrder: v2_0119_MiscellaneousChargeNotAssociatedWithAnOrder,
  NumberAssigned: v2_0119_NumberAssigned,
  NotificationReceived: v2_0119_NotificationReceived,
  NewOrderService: v2_0119_NewOrderService,
  OrderServiceCanceled: v2_0119_OrderServiceCanceled,
  OrderServiceDiscontinued: v2_0119_OrderServiceDiscontinued,
  OrderServiceReleased: v2_0119_OrderServiceReleased,
  OrderServiceRefilledAsRequested: v2_0119_OrderServiceRefilledAsRequested,
  OrderServiceHeld: v2_0119_OrderServiceHeld,
  OrderServiceAcceptedAndOK: v2_0119_OrderServiceAcceptedAndOK,
  NotificationOfOrderForOutsideDispense: v2_0119_NotificationOfOrderForOutsideDispense,
  ReleasedAsRequested: v2_0119_ReleasedAsRequested,
  ParentOrderService: v2_0119_ParentOrderService,
  PreviousResultsWithNewOrderService: v2_0119_PreviousResultsWithNewOrderService,
  NotificationOfReplacementOrderForOutsideDispense: v2_0119_NotificationOfReplacementOrderForOutsideDispense,
  RecommendationAccepted: v2_0119_RecommendationAccepted,
  RecommendedChange: v2_0119_RecommendedChange,
  RecommendationDeclined: v2_0119_RecommendationDeclined,
  ObservationsPerformedServiceToFollow: v2_0119_ObservationsPerformedServiceToFollow,
  RefillOrderServiceRequest: v2_0119_RefillOrderServiceRequest,
  ReleasePreviousHold: v2_0119_ReleasePreviousHold,
  ReplacementOrder: v2_0119_ReplacementOrder,
  OrderServiceReplaceRequest: v2_0119_OrderServiceReplaceRequest,
  ReplacedAsRequested: v2_0119_ReplacedAsRequested,
  RequestReceived: v2_0119_RequestReceived,
  ReplacedUnsolicited: v2_0119_ReplacedUnsolicited,
  StatusChanged: v2_0119_StatusChanged,
  SendOrderServiceNumber: v2_0119_SendOrderServiceNumber,
  SupplementedAsRequested: v2_0119_SupplementedAsRequested,
  ResponseToSendOrderServiceStatusRequest: v2_0119_ResponseToSendOrderServiceStatusRequest,
  SendOrderServiceStatusRequest: v2_0119_SendOrderServiceStatusRequest,
  SupplementThisOrder: v2_0119_SupplementThisOrder,
  UnableToAcceptOrderService: v2_0119_UnableToAcceptOrderService,
  UnableToCancel: v2_0119_UnableToCancel,
  UnableToDiscontinue: v2_0119_UnableToDiscontinue,
  UnableToRefill: v2_0119_UnableToRefill,
  UnableToPutOnHold: v2_0119_UnableToPutOnHold,
  UnableToReplace: v2_0119_UnableToReplace,
  UnlinkOrderServiceFromPatientCareProblemOrGoal: v2_0119_UnlinkOrderServiceFromPatientCareProblemOrGoal,
  UnableToRelease: v2_0119_UnableToRelease,
  UnableToChange: v2_0119_UnableToChange,
  ChangeOrderServiceRequest: v2_0119_ChangeOrderServiceRequest,
  ChangedAsRequested: v2_0119_ChangedAsRequested,
  OrderServiceChangedUnsol: v2_0119_OrderServiceChangedUnsol,
};
const v2_0121_SameAsRAlsoOtherAssociatedSegments: Coding = {
  code: 'D',
  display: 'Same as R, also other associated segments',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0121',
};
const v2_0121_ReportExceptionsOnly: Coding = {
  code: 'E',
  display: 'Report exceptions only',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0121',
};
const v2_0121_SameAsDPlusConfirmationsExplicitly: Coding = {
  code: 'F',
  display: 'Same as D, plus confirmations explicitly',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0121',
};
const v2_0121_OnlyTheMSASegmentIsReturned: Coding = {
  code: 'N',
  display: 'Only the MSA segment is returned',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0121',
};
const v2_0121_SameAsEAlsoReplacementAndParentChild: Coding = {
  code: 'R',
  display: 'Same as E, also Replacement and Parent-Child',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0121',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0121 ( RESPONSE FLAG)
 */
export const V20121 = {
  SameAsRAlsoOtherAssociatedSegments: v2_0121_SameAsRAlsoOtherAssociatedSegments,
  ReportExceptionsOnly: v2_0121_ReportExceptionsOnly,
  SameAsDPlusConfirmationsExplicitly: v2_0121_SameAsDPlusConfirmationsExplicitly,
  OnlyTheMSASegmentIsReturned: v2_0121_OnlyTheMSASegmentIsReturned,
  SameAsEAlsoReplacementAndParentChild: v2_0121_SameAsEAlsoReplacementAndParentChild,
};
const v2_0122_Charge: Coding = {
  code: 'CH',
  display: 'Charge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0122',
};
const v2_0122_Contract: Coding = {
  code: 'CO',
  display: 'Contract',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0122',
};
const v2_0122_Credit: Coding = {
  code: 'CR',
  display: 'Credit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0122',
};
const v2_0122_Department: Coding = {
  code: 'DP',
  display: 'Department',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0122',
};
const v2_0122_Grant: Coding = {
  code: 'GR',
  display: 'Grant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0122',
};
const v2_0122_NoCharge: Coding = {
  code: 'NC',
  display: 'No Charge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0122',
};
const v2_0122_Professional: Coding = {
  code: 'PC',
  display: 'Professional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0122',
};
const v2_0122_Research: Coding = {
  code: 'RS',
  display: 'Research',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0122',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0122 ( CHARGE TYPE)
 */
export const V20122 = {
  Charge: v2_0122_Charge,
  Contract: v2_0122_Contract,
  Credit: v2_0122_Credit,
  Department: v2_0122_Department,
  Grant: v2_0122_Grant,
  NoCharge: v2_0122_NoCharge,
  Professional: v2_0122_Professional,
  Research: v2_0122_Research,
};
const v2_0123_SomeButNotAllResultsAvailable: Coding = {
  code: 'A',
  display: 'Some, but not all, results available',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0123',
};
const v2_0123_CorrectedFinal: Coding = {
  code: 'C',
  display: 'Corrected, final',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0123',
};
const v2_0123_FinalResults: Coding = {
  code: 'F',
  display: 'Final results',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0123',
};
const v2_0123_NoResultsAvailableSpecimenReceivedProcedureIncomplete: Coding = {
  code: 'I',
  display: 'No results available; specimen received, procedure incomplete',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0123',
};
const v2_0123_CorrectedNotFinal: Coding = {
  code: 'M',
  display: 'Corrected, not final',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0123',
};
const v2_0123_ProcedureCompletedResultsPending: Coding = {
  code: 'N',
  display: 'Procedure completed, results pending',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0123',
};
const v2_0123_OrderReceivedSpecimenNotYetReceived: Coding = {
  code: 'O',
  display: 'Order received; specimen not yet received',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0123',
};
const v2_0123_Preliminary: Coding = {
  code: 'P',
  display: 'Preliminary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0123',
};
const v2_0123_ResultsStoredNotYetVerified: Coding = {
  code: 'R',
  display: 'Results stored; not yet verified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0123',
};
const v2_0123_NoResultsAvailableProcedureScheduledButNotDone: Coding = {
  code: 'S',
  display: 'No results available; procedure scheduled, but not done',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0123',
};
const v2_0123_NoResultsAvailableOrderCanceled: Coding = {
  code: 'X',
  display: 'No results available; Order canceled',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0123',
};
const v2_0123_NoOrderOnRecordForThisTest: Coding = {
  code: 'Y',
  display: 'No order on record for this test',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0123',
};
const v2_0123_NoRecordOfThisPatient: Coding = {
  code: 'Z',
  display: 'No record of this patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0123',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0123 ( RESULT STATUS - OBR)
 */
export const V20123 = {
  SomeButNotAllResultsAvailable: v2_0123_SomeButNotAllResultsAvailable,
  CorrectedFinal: v2_0123_CorrectedFinal,
  FinalResults: v2_0123_FinalResults,
  NoResultsAvailableSpecimenReceivedProcedureIncomplete: v2_0123_NoResultsAvailableSpecimenReceivedProcedureIncomplete,
  CorrectedNotFinal: v2_0123_CorrectedNotFinal,
  ProcedureCompletedResultsPending: v2_0123_ProcedureCompletedResultsPending,
  OrderReceivedSpecimenNotYetReceived: v2_0123_OrderReceivedSpecimenNotYetReceived,
  Preliminary: v2_0123_Preliminary,
  ResultsStoredNotYetVerified: v2_0123_ResultsStoredNotYetVerified,
  NoResultsAvailableProcedureScheduledButNotDone: v2_0123_NoResultsAvailableProcedureScheduledButNotDone,
  NoResultsAvailableOrderCanceled: v2_0123_NoResultsAvailableOrderCanceled,
  NoOrderOnRecordForThisTest: v2_0123_NoOrderOnRecordForThisTest,
  NoRecordOfThisPatient: v2_0123_NoRecordOfThisPatient,
};
const v2_0124_CartPatientTravelsOnCartOrGurney: Coding = {
  code: 'CART',
  display: 'Cart - patient travels on cart or gurney',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0124',
};
const v2_0124_TheExaminingDeviceGoesToPatientQuoteSLocation: Coding = {
  code: 'PORT',
  display: "The examining device goes to patient's location",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0124',
};
const v2_0124_PatientWalksToDiagnosticService: Coding = {
  code: 'WALK',
  display: 'Patient walks to diagnostic service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0124',
};
const v2_0124_Wheelchair: Coding = {
  code: 'WHLC',
  display: 'Wheelchair',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0124',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0124 ( TRANSPORTATION MODE)
 */
export const V20124 = {
  CartPatientTravelsOnCartOrGurney: v2_0124_CartPatientTravelsOnCartOrGurney,
  TheExaminingDeviceGoesToPatientQuoteSLocation: v2_0124_TheExaminingDeviceGoesToPatientQuoteSLocation,
  PatientWalksToDiagnosticService: v2_0124_PatientWalksToDiagnosticService,
  Wheelchair: v2_0124_Wheelchair,
};
const v2_0440_Address: Coding = {
  code: 'AD',
  display: 'Address',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_AuthorizationInformation: Coding = {
  code: 'AUI',
  display: 'Authorization information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ChargeCodeAndDate: Coding = {
  code: 'CCD',
  display: 'Charge code and date',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ChannelCalibrationParameters: Coding = {
  code: 'CCP',
  display: 'Channel calibration parameters',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ChannelDefinition: Coding = {
  code: 'CD',
  display: 'Channel definition',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_CodedElement: Coding = {
  code: 'CE',
  display: 'Coded element',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_CodedElementWithFormattedValues: Coding = {
  code: 'CF',
  display: 'Coded element with formatted values',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_CompositeIDWithCheckDigit: Coding = {
  code: 'CK',
  display: 'Composite ID with check digit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_Composite: Coding = {
  code: 'CM',
  display: 'Composite',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_CompositeIDNumberAndName: Coding = {
  code: 'CN',
  display: 'Composite ID number and name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_CodedWithNoExceptions: Coding = {
  code: 'CNE',
  display: 'Coded with no exceptions',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_CompositeIDNumberAndNameSimplified: Coding = {
  code: 'CNN',
  display: 'Composite ID number and name simplified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_CompositePrice: Coding = {
  code: 'CP',
  display: 'Composite price',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_CompositeQuantityWithUnits: Coding = {
  code: 'CQ',
  display: 'Composite quantity with units',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ChannelSensitivityAndUnits: Coding = {
  code: 'CSU',
  display: 'Channel sensitivity and units',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_CodedWithExceptions: Coding = {
  code: 'CWE',
  display: 'Coded with exceptions',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ExtendedCompositeIDWithCheckDigit: Coding = {
  code: 'CX',
  display: 'Extended composite ID with check digit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_DailyDeductibleInformation: Coding = {
  code: 'DDI',
  display: 'Daily deductible information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_DateAndInstitutionName: Coding = {
  code: 'DIN',
  display: 'Date and institution name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_DischargeToLocationAndDate: Coding = {
  code: 'DLD',
  display: 'Discharge to location and date',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_DriverQuoteSLicenseNumber: Coding = {
  code: 'DLN',
  display: "Driver's license number",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_Delta: Coding = {
  code: 'DLT',
  display: 'Delta',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_DateTimeRange: Coding = {
  code: 'DR',
  display: 'Date/time range',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_Date: Coding = {
  code: 'DT',
  display: 'Date',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_DateTime: Coding = {
  code: 'DTM',
  display: 'Date/time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_DayTypeAndNumber: Coding = {
  code: 'DTN',
  display: 'Day type and number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_EncapsulatedData: Coding = {
  code: 'ED',
  display: 'Encapsulated data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_EntityIdentifier: Coding = {
  code: 'EI',
  display: 'Entity identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_EntityIdentifierPair: Coding = {
  code: 'EIP',
  display: 'Entity identifier pair',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ErrorLocation: Coding = {
  code: 'ERL',
  display: 'Error location',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_FinancialClass: Coding = {
  code: 'FC',
  display: 'Financial class',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_FormattedText: Coding = {
  code: 'FT',
  display: 'Formatted text',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_GeneralTimingSpecification: Coding = {
  code: 'GTS',
  display: 'General timing specification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_HierarchicDesignator: Coding = {
  code: 'HD',
  display: 'Hierarchic designator',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_InsuranceCertificationDefinition: Coding = {
  code: 'ICD',
  display: 'Insurance certification definition',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_CodedValuesForHL7Tables: Coding = {
  code: 'ID',
  display: 'Coded values for HL7 tables',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_CodedValueForUserDefinedTables: Coding = {
  code: 'IS',
  display: 'Coded value for user-defined tables',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_JobCodeClass: Coding = {
  code: 'JCC',
  display: 'Job code/class',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_LocationWithAddressVariation1: Coding = {
  code: 'LA1',
  display: 'Location with address variation 1',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_LocationWithAddressVariation2: Coding = {
  code: 'LA2',
  display: 'Location with address variation 2',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_MultiplexedArray: Coding = {
  code: 'MA',
  display: 'Multiplexed array',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_Money: Coding = {
  code: 'MO',
  display: 'Money',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_MoneyAndChargeCode: Coding = {
  code: 'MOC',
  display: 'Money and charge code',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_MoneyOrPercentage: Coding = {
  code: 'MOP',
  display: 'Money or percentage',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_MessageType: Coding = {
  code: 'MSG',
  display: 'Message type',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_NumericArray: Coding = {
  code: 'NA',
  display: 'Numeric array',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_NameWithDateAndLocation: Coding = {
  code: 'NDL',
  display: 'Name with date and location',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_Numeric: Coding = {
  code: 'NM',
  display: 'Numeric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_NumericRange: Coding = {
  code: 'NR',
  display: 'Numeric range',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_OccurrenceCodeAndDate: Coding = {
  code: 'OCD',
  display: 'Occurrence code and date',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_OccurrenceSpanCodeAndDate: Coding = {
  code: 'OSP',
  display: 'Occurrence span code and date',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_PractitionerInstitutionalPrivileges: Coding = {
  code: 'PIP',
  display: 'Practitioner institutional privileges',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_PersonLocation: Coding = {
  code: 'PL',
  display: 'Person location',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_PractitionerLicenseOrOtherIDNumber: Coding = {
  code: 'PLN',
  display: 'Practitioner license or other ID number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_PersonName: Coding = {
  code: 'PN',
  display: 'Person name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_PerformingPersonTimeStamp: Coding = {
  code: 'PPN',
  display: 'Performing person time stamp',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ParentResultLink: Coding = {
  code: 'PRL',
  display: 'Parent result link',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ProcessingType: Coding = {
  code: 'PT',
  display: 'Processing type',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_PolicyTypeAndAmount: Coding = {
  code: 'PTA',
  display: 'Policy type and amount',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_QueryInputParameterList: Coding = {
  code: 'QIP',
  display: 'Query input parameter list',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_QuerySelectionCriteria: Coding = {
  code: 'QSC',
  display: 'Query selection criteria',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_RowColumnDefinition: Coding = {
  code: 'RCD',
  display: 'Row column definition',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ReferenceRange: Coding = {
  code: 'RFR',
  display: 'Reference range',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_RepeatInterval: Coding = {
  code: 'RI',
  display: 'Repeat interval',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_RoomCoverage: Coding = {
  code: 'RMC',
  display: 'Room coverage',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ReferencePointer: Coding = {
  code: 'RP',
  display: 'Reference pointer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_RepeatPattern: Coding = {
  code: 'RPT',
  display: 'Repeat pattern',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_SchedulingClassValuePair: Coding = {
  code: 'SCV',
  display: 'Scheduling class value pair',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_SequenceID: Coding = {
  code: 'SI',
  display: 'Sequence ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_StructuredNumeric: Coding = {
  code: 'SN',
  display: 'Structured numeric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_StringOfTelephoneNumberDigits: Coding = {
  code: 'SNM',
  display: 'String of telephone number digits',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_SpecialtyDescription: Coding = {
  code: 'SPD',
  display: 'Specialty description',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_SortOrder: Coding = {
  code: 'SRT',
  display: 'Sort order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_StringData: Coding = {
  code: 'ST',
  display: 'String data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_Time: Coding = {
  code: 'TM',
  display: 'Time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_TelephoneNumber: Coding = {
  code: 'TN',
  display: 'Telephone number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_TimingQuantity: Coding = {
  code: 'TQ',
  display: 'Timing/quantity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_TimeStamp: Coding = {
  code: 'TS',
  display: 'Time stamp',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_TextData: Coding = {
  code: 'TX',
  display: 'Text data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_UBValueCodeAndAmount: Coding = {
  code: 'UVC',
  display: 'UB value code and amount',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_VisitingHours: Coding = {
  code: 'VH',
  display: 'Visiting hours',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_VersionIdentifier: Coding = {
  code: 'VID',
  display: 'Version identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ValueRange: Coding = {
  code: 'VR',
  display: 'Value range',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ChannelIdentifier: Coding = {
  code: 'WVI',
  display: 'Channel Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_WaveformSource: Coding = {
  code: 'WVS',
  display: 'Waveform source',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ExtendedAddress: Coding = {
  code: 'XAD',
  display: 'Extended address',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ExtendedCompositeIDNumberAndNameForPersons: Coding = {
  code: 'XCN',
  display: 'Extended composite ID number and name for persons',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ExtendedCompositeNameAndIDNumberForOrganizations: Coding = {
  code: 'XON',
  display: 'Extended composite name and ID number for organizations',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ExtendedPersonName: Coding = {
  code: 'XPN',
  display: 'Extended person name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_ExtendedTelecommunicationsNumber: Coding = {
  code: 'XTN',
  display: 'Extended telecommunications number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0125 ( VALUE TYPE)
 */
export const V20125 = {
  Address: v2_0440_Address,
  AuthorizationInformation: v2_0440_AuthorizationInformation,
  ChargeCodeAndDate: v2_0440_ChargeCodeAndDate,
  ChannelCalibrationParameters: v2_0440_ChannelCalibrationParameters,
  ChannelDefinition: v2_0440_ChannelDefinition,
  CodedElement: v2_0440_CodedElement,
  CodedElementWithFormattedValues: v2_0440_CodedElementWithFormattedValues,
  CompositeIDWithCheckDigit: v2_0440_CompositeIDWithCheckDigit,
  Composite: v2_0440_Composite,
  CompositeIDNumberAndName: v2_0440_CompositeIDNumberAndName,
  CodedWithNoExceptions: v2_0440_CodedWithNoExceptions,
  CompositeIDNumberAndNameSimplified: v2_0440_CompositeIDNumberAndNameSimplified,
  CompositePrice: v2_0440_CompositePrice,
  CompositeQuantityWithUnits: v2_0440_CompositeQuantityWithUnits,
  ChannelSensitivityAndUnits: v2_0440_ChannelSensitivityAndUnits,
  CodedWithExceptions: v2_0440_CodedWithExceptions,
  ExtendedCompositeIDWithCheckDigit: v2_0440_ExtendedCompositeIDWithCheckDigit,
  DailyDeductibleInformation: v2_0440_DailyDeductibleInformation,
  DateAndInstitutionName: v2_0440_DateAndInstitutionName,
  DischargeToLocationAndDate: v2_0440_DischargeToLocationAndDate,
  DriverQuoteSLicenseNumber: v2_0440_DriverQuoteSLicenseNumber,
  Delta: v2_0440_Delta,
  DateTimeRange: v2_0440_DateTimeRange,
  Date: v2_0440_Date,
  DateTime: v2_0440_DateTime,
  DayTypeAndNumber: v2_0440_DayTypeAndNumber,
  EncapsulatedData: v2_0440_EncapsulatedData,
  EntityIdentifier: v2_0440_EntityIdentifier,
  EntityIdentifierPair: v2_0440_EntityIdentifierPair,
  ErrorLocation: v2_0440_ErrorLocation,
  FinancialClass: v2_0440_FinancialClass,
  FormattedText: v2_0440_FormattedText,
  GeneralTimingSpecification: v2_0440_GeneralTimingSpecification,
  HierarchicDesignator: v2_0440_HierarchicDesignator,
  InsuranceCertificationDefinition: v2_0440_InsuranceCertificationDefinition,
  CodedValuesForHL7Tables: v2_0440_CodedValuesForHL7Tables,
  CodedValueForUserDefinedTables: v2_0440_CodedValueForUserDefinedTables,
  JobCodeClass: v2_0440_JobCodeClass,
  LocationWithAddressVariation1: v2_0440_LocationWithAddressVariation1,
  LocationWithAddressVariation2: v2_0440_LocationWithAddressVariation2,
  MultiplexedArray: v2_0440_MultiplexedArray,
  Money: v2_0440_Money,
  MoneyAndChargeCode: v2_0440_MoneyAndChargeCode,
  MoneyOrPercentage: v2_0440_MoneyOrPercentage,
  MessageType: v2_0440_MessageType,
  NumericArray: v2_0440_NumericArray,
  NameWithDateAndLocation: v2_0440_NameWithDateAndLocation,
  Numeric: v2_0440_Numeric,
  NumericRange: v2_0440_NumericRange,
  OccurrenceCodeAndDate: v2_0440_OccurrenceCodeAndDate,
  OccurrenceSpanCodeAndDate: v2_0440_OccurrenceSpanCodeAndDate,
  PractitionerInstitutionalPrivileges: v2_0440_PractitionerInstitutionalPrivileges,
  PersonLocation: v2_0440_PersonLocation,
  PractitionerLicenseOrOtherIDNumber: v2_0440_PractitionerLicenseOrOtherIDNumber,
  PersonName: v2_0440_PersonName,
  PerformingPersonTimeStamp: v2_0440_PerformingPersonTimeStamp,
  ParentResultLink: v2_0440_ParentResultLink,
  ProcessingType: v2_0440_ProcessingType,
  PolicyTypeAndAmount: v2_0440_PolicyTypeAndAmount,
  QueryInputParameterList: v2_0440_QueryInputParameterList,
  QuerySelectionCriteria: v2_0440_QuerySelectionCriteria,
  RowColumnDefinition: v2_0440_RowColumnDefinition,
  ReferenceRange: v2_0440_ReferenceRange,
  RepeatInterval: v2_0440_RepeatInterval,
  RoomCoverage: v2_0440_RoomCoverage,
  ReferencePointer: v2_0440_ReferencePointer,
  RepeatPattern: v2_0440_RepeatPattern,
  SchedulingClassValuePair: v2_0440_SchedulingClassValuePair,
  SequenceID: v2_0440_SequenceID,
  StructuredNumeric: v2_0440_StructuredNumeric,
  StringOfTelephoneNumberDigits: v2_0440_StringOfTelephoneNumberDigits,
  SpecialtyDescription: v2_0440_SpecialtyDescription,
  SortOrder: v2_0440_SortOrder,
  StringData: v2_0440_StringData,
  Time: v2_0440_Time,
  TelephoneNumber: v2_0440_TelephoneNumber,
  TimingQuantity: v2_0440_TimingQuantity,
  TimeStamp: v2_0440_TimeStamp,
  TextData: v2_0440_TextData,
  UBValueCodeAndAmount: v2_0440_UBValueCodeAndAmount,
  VisitingHours: v2_0440_VisitingHours,
  VersionIdentifier: v2_0440_VersionIdentifier,
  ValueRange: v2_0440_ValueRange,
  ChannelIdentifier: v2_0440_ChannelIdentifier,
  WaveformSource: v2_0440_WaveformSource,
  ExtendedAddress: v2_0440_ExtendedAddress,
  ExtendedCompositeIDNumberAndNameForPersons: v2_0440_ExtendedCompositeIDNumberAndNameForPersons,
  ExtendedCompositeNameAndIDNumberForOrganizations: v2_0440_ExtendedCompositeNameAndIDNumberForOrganizations,
  ExtendedPersonName: v2_0440_ExtendedPersonName,
  ExtendedTelecommunicationsNumber: v2_0440_ExtendedTelecommunicationsNumber,
};
const v2_0126_Characters: Coding = {
  code: 'CH',
  display: 'Characters',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0126',
};
const v2_0126_Lines: Coding = {
  code: 'LI',
  display: 'Lines',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0126',
};
const v2_0126_Pages: Coding = {
  code: 'PG',
  display: 'Pages',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0126',
};
const v2_0126_Records: Coding = {
  code: 'RD',
  display: 'Records',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0126',
};
const v2_0126_LocallyDefined: Coding = {
  code: 'ZO',
  display: 'Locally defined',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0126',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0126 ( QUANTITY LIMITED REQUEST)
 */
export const V20126 = {
  Characters: v2_0126_Characters,
  Lines: v2_0126_Lines,
  Pages: v2_0126_Pages,
  Records: v2_0126_Records,
  LocallyDefined: v2_0126_LocallyDefined,
};
const v2_0127_AnimalAllergy: Coding = {
  code: 'AA',
  display: 'Animal Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0127',
};
const v2_0127_DrugAllergy: Coding = {
  code: 'DA',
  display: 'Drug allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0127',
};
const v2_0127_EnvironmentalAllergy: Coding = {
  code: 'EA',
  display: 'Environmental Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0127',
};
const v2_0127_FoodAllergy: Coding = {
  code: 'FA',
  display: 'Food allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0127',
};
const v2_0127_PollenAllergy: Coding = {
  code: 'LA',
  display: 'Pollen Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0127',
};
const v2_0127_MiscellaneousAllergy: Coding = {
  code: 'MA',
  display: 'Miscellaneous allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0127',
};
const v2_0127_MiscellaneousContraindication: Coding = {
  code: 'MC',
  display: 'Miscellaneous contraindication',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0127',
};
const v2_0127_PlantAllergy: Coding = {
  code: 'PA',
  display: 'Plant Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0127',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0127 ( ALLERGY TYPE)
 */
export const V20127 = {
  AnimalAllergy: v2_0127_AnimalAllergy,
  DrugAllergy: v2_0127_DrugAllergy,
  EnvironmentalAllergy: v2_0127_EnvironmentalAllergy,
  FoodAllergy: v2_0127_FoodAllergy,
  PollenAllergy: v2_0127_PollenAllergy,
  MiscellaneousAllergy: v2_0127_MiscellaneousAllergy,
  MiscellaneousContraindication: v2_0127_MiscellaneousContraindication,
  PlantAllergy: v2_0127_PlantAllergy,
};
const v2_0128_Mild: Coding = {
  code: 'MI',
  display: 'Mild',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0128',
};
const v2_0128_Moderate: Coding = {
  code: 'MO',
  display: 'Moderate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0128',
};
const v2_0128_Severe: Coding = {
  code: 'SV',
  display: 'Severe',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0128',
};
const v2_0128_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0128',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0128 ( ALLERGY SEVERITY)
 */
export const V20128 = {
  Mild: v2_0128_Mild,
  Moderate: v2_0128_Moderate,
  Severe: v2_0128_Severe,
  Unknown: v2_0128_Unknown,
};
const v2_0130_Home: Coding = {
  code: 'HO',
  display: 'Home',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0130',
};
const v2_0130_MobileUnit: Coding = {
  code: 'MO',
  display: 'Mobile Unit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0130',
};
const v2_0130_Phone: Coding = {
  code: 'PH',
  display: 'Phone',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0130',
};
const v2_0130_Teaching: Coding = {
  code: 'TE',
  display: 'Teaching',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0130',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0130 ( Visit user code)
 */
export const V20130 = {
  Home: v2_0130_Home,
  MobileUnit: v2_0130_MobileUnit,
  Phone: v2_0130_Phone,
  Teaching: v2_0130_Teaching,
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0131 ( Contact Role)
 */
export const V20131 = {
  EmergencyContact: v2_0131_EmergencyContact,
  Employer: v2_0131_Employer,
  FederalAgency: v2_0131_FederalAgency,
  InsuranceCompany: v2_0131_InsuranceCompany,
  NextOfKin: v2_0131_NextOfKin,
  Other: v2_0131_Other,
  StateAgency: v2_0131_StateAgency,
  Unknown: v2_0131_Unknown,
};
const v2_0135_ModifiedAssignment: Coding = {
  code: 'M',
  display: 'Modified assignment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0135',
};
const v2_0135_No: Coding = {
  code: 'N',
  display: 'No',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0135',
};
const v2_0135_Yes: Coding = {
  code: 'Y',
  display: 'Yes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0135',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0135 ( ASSIGNMENT OF BENEFITS)
 */
export const V20135 = {
  ModifiedAssignment: v2_0135_ModifiedAssignment,
  No: v2_0135_No,
  Yes: v2_0135_Yes,
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0136 ( Y/N Indicator)
 */
export const V20136 = {
  No: v2_0136_No,
  Yes: v2_0136_Yes,
};
const v2_0137_Employer: Coding = {
  code: 'E',
  display: 'Employer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0137',
};
const v2_0137_Guarantor: Coding = {
  code: 'G',
  display: 'Guarantor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0137',
};
const v2_0137_InsuranceCompany: Coding = {
  code: 'I',
  display: 'Insurance company',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0137',
};
const v2_0137_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0137',
};
const v2_0137_Patient: Coding = {
  code: 'P',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0137',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0137 ( MAIL CLAIM PARTY)
 */
export const V20137 = {
  Employer: v2_0137_Employer,
  Guarantor: v2_0137_Guarantor,
  InsuranceCompany: v2_0137_InsuranceCompany,
  Other: v2_0137_Other,
  Patient: v2_0137_Patient,
};
const v2_0140_AustralianArmy: Coding = {
  code: 'AUSA',
  display: 'Australian Army',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0140',
};
const v2_0140_AustralianAirForce: Coding = {
  code: 'AUSAF',
  display: 'Australian Air Force',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0140',
};
const v2_0140_AustralianNavy: Coding = {
  code: 'AUSN',
  display: 'Australian Navy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0140',
};
const v2_0140_NorthAtlanticTreatyOrganization: Coding = {
  code: 'NATO',
  display: 'North Atlantic Treaty Organization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0140',
};
const v2_0140_NationalOceanicAndAtmosphericAdministration: Coding = {
  code: 'NOAA',
  display: 'National Oceanic and Atmospheric Administration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0140',
};
const v2_0140_USArmy: Coding = {
  code: 'USA',
  display: 'US Army',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0140',
};
const v2_0140_USAirForce: Coding = {
  code: 'USAF',
  display: 'US Air Force',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0140',
};
const v2_0140_USCoastGuard: Coding = {
  code: 'USCG',
  display: 'US Coast Guard',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0140',
};
const v2_0140_USMarineCorps: Coding = {
  code: 'USMC',
  display: 'US Marine Corps',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0140',
};
const v2_0140_USNavy: Coding = {
  code: 'USN',
  display: 'US Navy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0140',
};
const v2_0140_USPublicHealthService: Coding = {
  code: 'USPHS',
  display: 'US Public Health Service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0140',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0140 ( Military service)
 */
export const V20140 = {
  AustralianArmy: v2_0140_AustralianArmy,
  AustralianAirForce: v2_0140_AustralianAirForce,
  AustralianNavy: v2_0140_AustralianNavy,
  NorthAtlanticTreatyOrganization: v2_0140_NorthAtlanticTreatyOrganization,
  NationalOceanicAndAtmosphericAdministration: v2_0140_NationalOceanicAndAtmosphericAdministration,
  USArmy: v2_0140_USArmy,
  USAirForce: v2_0140_USAirForce,
  USCoastGuard: v2_0140_USCoastGuard,
  USMarineCorps: v2_0140_USMarineCorps,
  USNavy: v2_0140_USNavy,
  USPublicHealthService: v2_0140_USPublicHealthService,
};
const v2_0142_ActiveDuty: Coding = {
  code: 'ACT',
  display: 'Active duty',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0142',
};
const v2_0142_Deceased: Coding = {
  code: 'DEC',
  display: 'Deceased',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0142',
};
const v2_0142_Retired: Coding = {
  code: 'RET',
  display: 'Retired',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0142',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0142 ( Military status)
 */
export const V20142 = {
  ActiveDuty: v2_0142_ActiveDuty,
  Deceased: v2_0142_Deceased,
  Retired: v2_0142_Retired,
};
const v2_0144_InsuranceCompany: Coding = {
  code: '1',
  display: 'Insurance company',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0144',
};
const v2_0144_Employer: Coding = {
  code: '2',
  display: 'Employer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0144',
};
const v2_0144_InsuredPresentedPolicy: Coding = {
  code: '3',
  display: 'Insured presented policy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0144',
};
const v2_0144_InsuredPresentedCard: Coding = {
  code: '4',
  display: 'Insured presented card',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0144',
};
const v2_0144_SignedStatementOnFile: Coding = {
  code: '5',
  display: 'Signed statement on file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0144',
};
const v2_0144_VerbalInformation: Coding = {
  code: '6',
  display: 'Verbal information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0144',
};
const v2_0144_None: Coding = {
  code: '7',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0144',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0144 ( ELIGIBILITY SOURCE)
 */
export const V20144 = {
  InsuranceCompany: v2_0144_InsuranceCompany,
  Employer: v2_0144_Employer,
  InsuredPresentedPolicy: v2_0144_InsuredPresentedPolicy,
  InsuredPresentedCard: v2_0144_InsuredPresentedCard,
  SignedStatementOnFile: v2_0144_SignedStatementOnFile,
  VerbalInformation: v2_0144_VerbalInformation,
  None: v2_0144_None,
};
const v2_0145_SecondIntensiveCareUnit: Coding = {
  code: '2ICU',
  display: 'Second intensive care unit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0145',
};
const v2_0145_SecondPrivateRoom: Coding = {
  code: '2PRI',
  display: 'Second private room',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0145',
};
const v2_0145_SecondSemiPrivateRoom: Coding = {
  code: '2SPR',
  display: 'Second semi-private room',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0145',
};
const v2_0145_IntensiveCareUnit: Coding = {
  code: 'ICU',
  display: 'Intensive care unit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0145',
};
const v2_0145_PrivateRoom: Coding = {
  code: 'PRI',
  display: 'Private room',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0145',
};
const v2_0145_SemiPrivateRoom: Coding = {
  code: 'SPR',
  display: 'Semi-private room',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0145',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0145 ( Room Type)
 */
export const V20145 = {
  SecondIntensiveCareUnit: v2_0145_SecondIntensiveCareUnit,
  SecondPrivateRoom: v2_0145_SecondPrivateRoom,
  SecondSemiPrivateRoom: v2_0145_SecondSemiPrivateRoom,
  IntensiveCareUnit: v2_0145_IntensiveCareUnit,
  PrivateRoom: v2_0145_PrivateRoom,
  SemiPrivateRoom: v2_0145_SemiPrivateRoom,
};
const v2_0146_Differential: Coding = {
  code: 'DF',
  display: 'Differential',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0146',
};
const v2_0146_Limit: Coding = {
  code: 'LM',
  display: 'Limit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0146',
};
const v2_0146_Percentage: Coding = {
  code: 'PC',
  display: 'Percentage',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0146',
};
const v2_0146_Rate: Coding = {
  code: 'RT',
  display: 'Rate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0146',
};
const v2_0146_Unlimited: Coding = {
  code: 'UL',
  display: 'Unlimited',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0146',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0146 ( AMOUNT TYPE)
 */
export const V20146 = {
  Differential: v2_0146_Differential,
  Limit: v2_0146_Limit,
  Percentage: v2_0146_Percentage,
  Rate: v2_0146_Rate,
  Unlimited: v2_0146_Unlimited,
};
const v2_0147_SecondAncillary: Coding = {
  code: '2ANC',
  display: 'Second ancillary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0147',
};
const v2_0147_SecondMajorMedical: Coding = {
  code: '2MMD',
  display: 'Second major medical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0147',
};
const v2_0147_ThirdMajorMedical: Coding = {
  code: '3MMD',
  display: 'Third major medical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0147',
};
const v2_0147_Ancillary: Coding = {
  code: 'ANC',
  display: 'Ancillary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0147',
};
const v2_0147_MajorMedical: Coding = {
  code: 'MMD',
  display: 'Major medical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0147',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0147 ( POLICY TYPE)
 */
export const V20147 = {
  SecondAncillary: v2_0147_SecondAncillary,
  SecondMajorMedical: v2_0147_SecondMajorMedical,
  ThirdMajorMedical: v2_0147_ThirdMajorMedical,
  Ancillary: v2_0147_Ancillary,
  MajorMedical: v2_0147_MajorMedical,
};
const v2_0148_CurrencyAmount: Coding = {
  code: 'AT',
  display: 'Currency amount',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0148',
};
const v2_0148_Percentage: Coding = {
  code: 'PC',
  display: 'Percentage',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0148',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0148 ( PENALTY TYPE)
 */
export const V20148 = {
  CurrencyAmount: v2_0148_CurrencyAmount,
  Percentage: v2_0148_Percentage,
};
const v2_0149_Approved: Coding = {
  code: 'AP',
  display: 'Approved',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0149',
};
const v2_0149_Denied: Coding = {
  code: 'DE',
  display: 'Denied',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0149',
};
const v2_0149_Pending: Coding = {
  code: 'PE',
  display: 'Pending',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0149',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0149 ( DAY TYPE)
 */
export const V20149 = {
  Approved: v2_0149_Approved,
  Denied: v2_0149_Denied,
  Pending: v2_0149_Pending,
};
const v2_0150_Emergency: Coding = {
  code: 'ER',
  display: 'Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0150',
};
const v2_0150_InpatientElective: Coding = {
  code: 'IPE',
  display: 'Inpatient elective',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0150',
};
const v2_0150_OutpatientElective: Coding = {
  code: 'OPE',
  display: 'Outpatient elective',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0150',
};
const v2_0150_Urgent: Coding = {
  code: 'UR',
  display: 'Urgent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0150',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0150 ( PRECERTIFICATION PATIENT TYPE)
 */
export const V20150 = {
  Emergency: v2_0150_Emergency,
  InpatientElective: v2_0150_InpatientElective,
  OutpatientElective: v2_0150_OutpatientElective,
  Urgent: v2_0150_Urgent,
};
const v2_0155_Always: Coding = {
  code: 'AL',
  display: 'Always',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0155',
};
const v2_0155_ErrorRejectConditionsOnly: Coding = {
  code: 'ER',
  display: 'Error/reject conditions only',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0155',
};
const v2_0155_Never: Coding = {
  code: 'NE',
  display: 'Never',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0155',
};
const v2_0155_SuccessfulCompletionOnly: Coding = {
  code: 'SU',
  display: 'Successful completion only',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0155',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0155 ( ACCEPT/APPLICATION ACKNOWLEDGEMENT CONDITIONS)
 */
export const V20155 = {
  Always: v2_0155_Always,
  ErrorRejectConditionsOnly: v2_0155_ErrorRejectConditionsOnly,
  Never: v2_0155_Never,
  SuccessfulCompletionOnly: v2_0155_SuccessfulCompletionOnly,
};
const v2_0159_Diet: Coding = {
  code: 'D',
  display: 'Diet',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0159',
};
const v2_0159_Preference: Coding = {
  code: 'P',
  display: 'Preference',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0159',
};
const v2_0159_Supplement: Coding = {
  code: 'S',
  display: 'Supplement',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0159',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0159 ( DIET TYPE)
 */
export const V20159 = {
  Diet: v2_0159_Diet,
  Preference: v2_0159_Preference,
  Supplement: v2_0159_Supplement,
};
const v2_0160_EarlyTray: Coding = {
  code: 'EARLY',
  display: 'Early tray',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0160',
};
const v2_0160_GuestTray: Coding = {
  code: 'GUEST',
  display: 'Guest tray',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0160',
};
const v2_0160_LateTray: Coding = {
  code: 'LATE',
  display: 'Late tray',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0160',
};
const v2_0160_TrayMessageOnly: Coding = {
  code: 'MSG',
  display: 'Tray message only',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0160',
};
const v2_0160_NoTray: Coding = {
  code: 'NO',
  display: 'No tray',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0160',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0160 ( TRAY TYPE)
 */
export const V20160 = {
  EarlyTray: v2_0160_EarlyTray,
  GuestTray: v2_0160_GuestTray,
  LateTray: v2_0160_LateTray,
  TrayMessageOnly: v2_0160_TrayMessageOnly,
  NoTray: v2_0160_NoTray,
};
const v2_0161_AllowGenericSubstitutions: Coding = {
  code: 'G',
  display: 'Allow generic substitutions.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0161',
};
const v2_0161_SubstitutionsAreNOTAuthorizedThisIsTheDefaultNull: Coding = {
  code: 'N',
  display: 'Substitutions are NOT authorized.  (This is the default - null.)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0161',
};
const v2_0161_AllowTherapeuticSubstitutions: Coding = {
  code: 'T',
  display: 'Allow therapeutic substitutions',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0161',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0161 ( ALLOW SUBSTITUTION)
 */
export const V20161 = {
  AllowGenericSubstitutions: v2_0161_AllowGenericSubstitutions,
  SubstitutionsAreNOTAuthorizedThisIsTheDefaultNull: v2_0161_SubstitutionsAreNOTAuthorizedThisIsTheDefaultNull,
  AllowTherapeuticSubstitutions: v2_0161_AllowTherapeuticSubstitutions,
};
const v2_0162_ApplyExternally: Coding = {
  code: 'AP',
  display: 'Apply Externally',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Buccal: Coding = {
  code: 'B',
  display: 'Buccal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Dental: Coding = {
  code: 'DT',
  display: 'Dental',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Epidural: Coding = {
  code: 'EP',
  display: 'Epidural',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_EndotrachialTube: Coding = {
  code: 'ET',
  display: 'Endotrachial Tube',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_GastrostomyTube: Coding = {
  code: 'GTT',
  display: 'Gastrostomy Tube',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_GUIrrigant: Coding = {
  code: 'GU',
  display: 'GU Irrigant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_IntraArterial: Coding = {
  code: 'IA',
  display: 'Intra-arterial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Intrabursal: Coding = {
  code: 'IB',
  display: 'Intrabursal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Intracardiac: Coding = {
  code: 'IC',
  display: 'Intracardiac',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_IntracervicalUterus: Coding = {
  code: 'ICV',
  display: 'Intracervical (uterus)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Intradermal: Coding = {
  code: 'ID',
  display: 'Intradermal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Inhalation: Coding = {
  code: 'IH',
  display: 'Inhalation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_IntrahepaticArtery: Coding = {
  code: 'IHA',
  display: 'Intrahepatic Artery',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Intramuscular: Coding = {
  code: 'IM',
  display: 'Intramuscular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_ImmerseSoakBodyPart: Coding = {
  code: 'IMR',
  display: 'Immerse (Soak) Body Part',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Intranasal: Coding = {
  code: 'IN',
  display: 'Intranasal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Intraocular: Coding = {
  code: 'IO',
  display: 'Intraocular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Intraperitoneal: Coding = {
  code: 'IP',
  display: 'Intraperitoneal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Intrasynovial: Coding = {
  code: 'IS',
  display: 'Intrasynovial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Intrathecal: Coding = {
  code: 'IT',
  display: 'Intrathecal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Intrauterine: Coding = {
  code: 'IU',
  display: 'Intrauterine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Intravenous: Coding = {
  code: 'IV',
  display: 'Intravenous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_MucousMembrane: Coding = {
  code: 'MM',
  display: 'Mucous Membrane',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_MouthThroat: Coding = {
  code: 'MTH',
  display: 'Mouth/Throat',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Nasogastric: Coding = {
  code: 'NG',
  display: 'Nasogastric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_NasalProngs: Coding = {
  code: 'NP',
  display: 'Nasal Prongs',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Nasal: Coding = {
  code: 'NS',
  display: 'Nasal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_NasotrachialTube: Coding = {
  code: 'NT',
  display: 'Nasotrachial Tube',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Ophthalmic: Coding = {
  code: 'OP',
  display: 'Ophthalmic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Otic: Coding = {
  code: 'OT',
  display: 'Otic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_OtherMiscellaneous: Coding = {
  code: 'OTH',
  display: 'Other/Miscellaneous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Perfusion: Coding = {
  code: 'PF',
  display: 'Perfusion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Oral: Coding = {
  code: 'PO',
  display: 'Oral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Rectal: Coding = {
  code: 'PR',
  display: 'Rectal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_RebreatherMask: Coding = {
  code: 'RM',
  display: 'Rebreather Mask',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Subcutaneous: Coding = {
  code: 'SC',
  display: 'Subcutaneous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_SoakedDressing: Coding = {
  code: 'SD',
  display: 'Soaked Dressing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Sublingual: Coding = {
  code: 'SL',
  display: 'Sublingual',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Transdermal: Coding = {
  code: 'TD',
  display: 'Transdermal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Translingual: Coding = {
  code: 'TL',
  display: 'Translingual',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Topical: Coding = {
  code: 'TP',
  display: 'Topical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Tracheostomy: Coding = {
  code: 'TRA',
  display: 'Tracheostomy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Urethral: Coding = {
  code: 'UR',
  display: 'Urethral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Vaginal: Coding = {
  code: 'VG',
  display: 'Vaginal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Ventimask: Coding = {
  code: 'VM',
  display: 'Ventimask',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
const v2_0162_Wound: Coding = {
  code: 'WND',
  display: 'Wound',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0162',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0162 ( ROUTE OF ADMINISTRATION)
 */
export const V20162 = {
  ApplyExternally: v2_0162_ApplyExternally,
  Buccal: v2_0162_Buccal,
  Dental: v2_0162_Dental,
  Epidural: v2_0162_Epidural,
  EndotrachialTube: v2_0162_EndotrachialTube,
  GastrostomyTube: v2_0162_GastrostomyTube,
  GUIrrigant: v2_0162_GUIrrigant,
  IntraArterial: v2_0162_IntraArterial,
  Intrabursal: v2_0162_Intrabursal,
  Intracardiac: v2_0162_Intracardiac,
  IntracervicalUterus: v2_0162_IntracervicalUterus,
  Intradermal: v2_0162_Intradermal,
  Inhalation: v2_0162_Inhalation,
  IntrahepaticArtery: v2_0162_IntrahepaticArtery,
  Intramuscular: v2_0162_Intramuscular,
  ImmerseSoakBodyPart: v2_0162_ImmerseSoakBodyPart,
  Intranasal: v2_0162_Intranasal,
  Intraocular: v2_0162_Intraocular,
  Intraperitoneal: v2_0162_Intraperitoneal,
  Intrasynovial: v2_0162_Intrasynovial,
  Intrathecal: v2_0162_Intrathecal,
  Intrauterine: v2_0162_Intrauterine,
  Intravenous: v2_0162_Intravenous,
  MucousMembrane: v2_0162_MucousMembrane,
  MouthThroat: v2_0162_MouthThroat,
  Nasogastric: v2_0162_Nasogastric,
  NasalProngs: v2_0162_NasalProngs,
  Nasal: v2_0162_Nasal,
  NasotrachialTube: v2_0162_NasotrachialTube,
  Ophthalmic: v2_0162_Ophthalmic,
  Otic: v2_0162_Otic,
  OtherMiscellaneous: v2_0162_OtherMiscellaneous,
  Perfusion: v2_0162_Perfusion,
  Oral: v2_0162_Oral,
  Rectal: v2_0162_Rectal,
  RebreatherMask: v2_0162_RebreatherMask,
  Subcutaneous: v2_0162_Subcutaneous,
  SoakedDressing: v2_0162_SoakedDressing,
  Sublingual: v2_0162_Sublingual,
  Transdermal: v2_0162_Transdermal,
  Translingual: v2_0162_Translingual,
  Topical: v2_0162_Topical,
  Tracheostomy: v2_0162_Tracheostomy,
  Urethral: v2_0162_Urethral,
  Vaginal: v2_0162_Vaginal,
  Ventimask: v2_0162_Ventimask,
  Wound: v2_0162_Wound,
};
const v2_0163_BilateralEars: Coding = {
  code: 'BE',
  display: 'Bilateral Ears',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_BilateralNares: Coding = {
  code: 'BN',
  display: 'Bilateral Nares',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_Buttock: Coding = {
  code: 'BU',
  display: 'Buttock',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_ChestTube: Coding = {
  code: 'CT',
  display: 'Chest Tube',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftArm: Coding = {
  code: 'LA',
  display: 'Left Arm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftAnteriorChest: Coding = {
  code: 'LAC',
  display: 'Left Anterior Chest',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftAntecubitalFossa: Coding = {
  code: 'LACF',
  display: 'Left Antecubital Fossa',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftDeltoid: Coding = {
  code: 'LD',
  display: 'Left Deltoid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftEar: Coding = {
  code: 'LE',
  display: 'Left Ear',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftExternalJugular: Coding = {
  code: 'LEJ',
  display: 'Left External Jugular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftFoot: Coding = {
  code: 'LF',
  display: 'Left Foot',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftGluteusMedius: Coding = {
  code: 'LG',
  display: 'Left Gluteus Medius',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftHand: Coding = {
  code: 'LH',
  display: 'Left Hand',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftInternalJugular: Coding = {
  code: 'LIJ',
  display: 'Left Internal Jugular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftLowerAbdQuadrant: Coding = {
  code: 'LLAQ',
  display: 'Left Lower Abd Quadrant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftLowerForearm: Coding = {
  code: 'LLFA',
  display: 'Left Lower Forearm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftMidForearm: Coding = {
  code: 'LMFA',
  display: 'Left Mid Forearm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftNaris: Coding = {
  code: 'LN',
  display: 'Left Naris',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftPosteriorChest: Coding = {
  code: 'LPC',
  display: 'Left Posterior Chest',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftSubclavian: Coding = {
  code: 'LSC',
  display: 'Left Subclavian',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftThigh: Coding = {
  code: 'LT',
  display: 'Left Thigh',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftUpperArm: Coding = {
  code: 'LUA',
  display: 'Left Upper Arm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftUpperAbdQuadrant: Coding = {
  code: 'LUAQ',
  display: 'Left Upper Abd Quadrant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftUpperForearm: Coding = {
  code: 'LUFA',
  display: 'Left Upper Forearm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftVentragluteal: Coding = {
  code: 'LVG',
  display: 'Left Ventragluteal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftVastusLateralis: Coding = {
  code: 'LVL',
  display: 'Left Vastus Lateralis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_Nebulized: Coding = {
  code: 'NB',
  display: 'Nebulized',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightEye: Coding = {
  code: 'OD',
  display: 'Right Eye',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_LeftEye: Coding = {
  code: 'OS',
  display: 'Left Eye',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_BilateralEyes: Coding = {
  code: 'OU',
  display: 'Bilateral Eyes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_Perianal: Coding = {
  code: 'PA',
  display: 'Perianal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_Perineal: Coding = {
  code: 'PERIN',
  display: 'Perineal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightArm: Coding = {
  code: 'RA',
  display: 'Right Arm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightAnteriorChest: Coding = {
  code: 'RAC',
  display: 'Right Anterior Chest',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightAntecubitalFossa: Coding = {
  code: 'RACF',
  display: 'Right Antecubital Fossa',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightDeltoid: Coding = {
  code: 'RD',
  display: 'Right Deltoid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightEar: Coding = {
  code: 'RE',
  display: 'Right Ear',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightExternalJugular: Coding = {
  code: 'REJ',
  display: 'Right External Jugular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightFoot: Coding = {
  code: 'RF',
  display: 'Right Foot',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightGluteusMedius: Coding = {
  code: 'RG',
  display: 'Right Gluteus Medius',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightHand: Coding = {
  code: 'RH',
  display: 'Right Hand',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightInternalJugular: Coding = {
  code: 'RIJ',
  display: 'Right Internal Jugular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RtLowerAbdQuadrant: Coding = {
  code: 'RLAQ',
  display: 'Rt Lower Abd Quadrant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightLowerForearm: Coding = {
  code: 'RLFA',
  display: 'Right Lower Forearm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightMidForearm: Coding = {
  code: 'RMFA',
  display: 'Right Mid Forearm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightNaris: Coding = {
  code: 'RN',
  display: 'Right Naris',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightPosteriorChest: Coding = {
  code: 'RPC',
  display: 'Right Posterior Chest',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightSubclavian: Coding = {
  code: 'RSC',
  display: 'Right Subclavian',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightThigh: Coding = {
  code: 'RT',
  display: 'Right Thigh',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightUpperArm: Coding = {
  code: 'RUA',
  display: 'Right Upper Arm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightUpperAbdQuadrant: Coding = {
  code: 'RUAQ',
  display: 'Right Upper Abd Quadrant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightUpperForearm: Coding = {
  code: 'RUFA',
  display: 'Right Upper Forearm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightVentragluteal: Coding = {
  code: 'RVG',
  display: 'Right Ventragluteal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
const v2_0163_RightVastusLateralis: Coding = {
  code: 'RVL',
  display: 'Right Vastus Lateralis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0163',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0163 ( ADMINISTRIVE SITE)
 */
export const V20163 = {
  BilateralEars: v2_0163_BilateralEars,
  BilateralNares: v2_0163_BilateralNares,
  Buttock: v2_0163_Buttock,
  ChestTube: v2_0163_ChestTube,
  LeftArm: v2_0163_LeftArm,
  LeftAnteriorChest: v2_0163_LeftAnteriorChest,
  LeftAntecubitalFossa: v2_0163_LeftAntecubitalFossa,
  LeftDeltoid: v2_0163_LeftDeltoid,
  LeftEar: v2_0163_LeftEar,
  LeftExternalJugular: v2_0163_LeftExternalJugular,
  LeftFoot: v2_0163_LeftFoot,
  LeftGluteusMedius: v2_0163_LeftGluteusMedius,
  LeftHand: v2_0163_LeftHand,
  LeftInternalJugular: v2_0163_LeftInternalJugular,
  LeftLowerAbdQuadrant: v2_0163_LeftLowerAbdQuadrant,
  LeftLowerForearm: v2_0163_LeftLowerForearm,
  LeftMidForearm: v2_0163_LeftMidForearm,
  LeftNaris: v2_0163_LeftNaris,
  LeftPosteriorChest: v2_0163_LeftPosteriorChest,
  LeftSubclavian: v2_0163_LeftSubclavian,
  LeftThigh: v2_0163_LeftThigh,
  LeftUpperArm: v2_0163_LeftUpperArm,
  LeftUpperAbdQuadrant: v2_0163_LeftUpperAbdQuadrant,
  LeftUpperForearm: v2_0163_LeftUpperForearm,
  LeftVentragluteal: v2_0163_LeftVentragluteal,
  LeftVastusLateralis: v2_0163_LeftVastusLateralis,
  Nebulized: v2_0163_Nebulized,
  RightEye: v2_0163_RightEye,
  LeftEye: v2_0163_LeftEye,
  BilateralEyes: v2_0163_BilateralEyes,
  Perianal: v2_0163_Perianal,
  Perineal: v2_0163_Perineal,
  RightArm: v2_0163_RightArm,
  RightAnteriorChest: v2_0163_RightAnteriorChest,
  RightAntecubitalFossa: v2_0163_RightAntecubitalFossa,
  RightDeltoid: v2_0163_RightDeltoid,
  RightEar: v2_0163_RightEar,
  RightExternalJugular: v2_0163_RightExternalJugular,
  RightFoot: v2_0163_RightFoot,
  RightGluteusMedius: v2_0163_RightGluteusMedius,
  RightHand: v2_0163_RightHand,
  RightInternalJugular: v2_0163_RightInternalJugular,
  RtLowerAbdQuadrant: v2_0163_RtLowerAbdQuadrant,
  RightLowerForearm: v2_0163_RightLowerForearm,
  RightMidForearm: v2_0163_RightMidForearm,
  RightNaris: v2_0163_RightNaris,
  RightPosteriorChest: v2_0163_RightPosteriorChest,
  RightSubclavian: v2_0163_RightSubclavian,
  RightThigh: v2_0163_RightThigh,
  RightUpperArm: v2_0163_RightUpperArm,
  RightUpperAbdQuadrant: v2_0163_RightUpperAbdQuadrant,
  RightUpperForearm: v2_0163_RightUpperForearm,
  RightVentragluteal: v2_0163_RightVentragluteal,
  RightVastusLateralis: v2_0163_RightVastusLateralis,
};
const v2_0164_Applicator: Coding = {
  code: 'AP',
  display: 'Applicator',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0164',
};
const v2_0164_Buretrol: Coding = {
  code: 'BT',
  display: 'Buretrol',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0164',
};
const v2_0164_HeparinLock: Coding = {
  code: 'HL',
  display: 'Heparin Lock',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0164',
};
const v2_0164_IPPB: Coding = {
  code: 'IPPB',
  display: 'IPPB',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0164',
};
const v2_0164_IVPump: Coding = {
  code: 'IVP',
  display: 'IV Pump',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0164',
};
const v2_0164_IVSoluset: Coding = {
  code: 'IVS',
  display: 'IV Soluset',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0164',
};
const v2_0164_MeteredInhaler: Coding = {
  code: 'MI',
  display: 'Metered Inhaler',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0164',
};
const v2_0164_Nebulizer: Coding = {
  code: 'NEB',
  display: 'Nebulizer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0164',
};
const v2_0164_PCAPump: Coding = {
  code: 'PCA',
  display: 'PCA Pump',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0164',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0164 ( ADMINISTRATION DEVICE)
 */
export const V20164 = {
  Applicator: v2_0164_Applicator,
  Buretrol: v2_0164_Buretrol,
  HeparinLock: v2_0164_HeparinLock,
  IPPB: v2_0164_IPPB,
  IVPump: v2_0164_IVPump,
  IVSoluset: v2_0164_IVSoluset,
  MeteredInhaler: v2_0164_MeteredInhaler,
  Nebulizer: v2_0164_Nebulizer,
  PCAPump: v2_0164_PCAPump,
};
const v2_0165_Chew: Coding = {
  code: 'CH',
  display: 'Chew',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_Dissolve: Coding = {
  code: 'DI',
  display: 'Dissolve',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_Dust: Coding = {
  code: 'DU',
  display: 'Dust',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_Infiltrate: Coding = {
  code: 'IF',
  display: 'Infiltrate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_Irrigate: Coding = {
  code: 'IR',
  display: 'Irrigate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_Insert: Coding = {
  code: 'IS',
  display: 'Insert',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_IVPush: Coding = {
  code: 'IVP',
  display: 'IV Push',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_IVPiggyback: Coding = {
  code: 'IVPB',
  display: 'IV Piggyback',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_Nebulized: Coding = {
  code: 'NB',
  display: 'Nebulized',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_Perfuse: Coding = {
  code: 'PF',
  display: 'Perfuse',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_Paint: Coding = {
  code: 'PT',
  display: 'Paint',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_Shampoo: Coding = {
  code: 'SH',
  display: 'Shampoo',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_Soak: Coding = {
  code: 'SO',
  display: 'Soak',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_Wash: Coding = {
  code: 'WA',
  display: 'Wash',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
const v2_0165_Wipe: Coding = {
  code: 'WI',
  display: 'Wipe',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0165',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0165 ( ADMINISTRATION METHOD)
 */
export const V20165 = {
  Chew: v2_0165_Chew,
  Dissolve: v2_0165_Dissolve,
  Dust: v2_0165_Dust,
  Infiltrate: v2_0165_Infiltrate,
  Irrigate: v2_0165_Irrigate,
  Insert: v2_0165_Insert,
  IVPush: v2_0165_IVPush,
  IVPiggyback: v2_0165_IVPiggyback,
  Nebulized: v2_0165_Nebulized,
  Perfuse: v2_0165_Perfuse,
  Paint: v2_0165_Paint,
  Shampoo: v2_0165_Shampoo,
  Soak: v2_0165_Soak,
  Wash: v2_0165_Wash,
  Wipe: v2_0165_Wipe,
};
const v2_0166_Additive: Coding = {
  code: 'A',
  display: 'Additive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0166',
};
const v2_0166_Base: Coding = {
  code: 'B',
  display: 'Base',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0166',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0166 ( RX COMPONENT TYPE)
 */
export const V20166 = {
  Additive: v2_0166_Additive,
  Base: v2_0166_Base,
};
const v2_0167_NoProductSelectionIndicated: Coding = {
  code: '0',
  display: 'No product selection indicated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0167',
};
const v2_0167_SubstitutionNotAllowedByPrescriber: Coding = {
  code: '1',
  display: 'Substitution not allowed by prescriber',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0167',
};
const v2_0167_SubstitutionAllowedPatientRequestedProductDispensed: Coding = {
  code: '2',
  display: 'Substitution allowed - patient requested product dispensed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0167',
};
const v2_0167_SubstitutionAllowedPharmacistSelectedProductDispensed: Coding = {
  code: '3',
  display: 'Substitution allowed - pharmacist selected product dispensed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0167',
};
const v2_0167_SubstitutionAllowedGenericDrugNotInStock: Coding = {
  code: '4',
  display: 'Substitution allowed - generic drug not in stock',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0167',
};
const v2_0167_SubstitutionAllowedBrandDrugDispensedAsAGeneric: Coding = {
  code: '5',
  display: 'Substitution allowed - brand drug dispensed as a generic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0167',
};
const v2_0167_SubstitutionNotAllowedBrandDrugMandatedByLaw: Coding = {
  code: '7',
  display: 'Substitution not allowed - brand drug mandated by law',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0167',
};
const v2_0167_SubstitutionAllowedGenericDrugNotAvailableInMarketplace: Coding = {
  code: '8',
  display: 'Substitution allowed - generic drug not available in marketplace',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0167',
};
const v2_0167_AGenericSubstitutionWasDispensed: Coding = {
  code: 'G',
  display: 'A generic substitution was dispensed.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0167',
};
const v2_0167_NoSubstituteWasDispensedThisIsEquivalentToTheDefaultNullValue: Coding = {
  code: 'N',
  display: 'No substitute was dispensed.  This is equivalent to the default (null) value.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0167',
};
const v2_0167_ATherapeuticSubstitutionWasDispensed: Coding = {
  code: 'T',
  display: 'A therapeutic substitution was dispensed.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0167',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0167 ( SUBSTITUTION STATUS)
 */
export const V20167 = {
  NoProductSelectionIndicated: v2_0167_NoProductSelectionIndicated,
  SubstitutionNotAllowedByPrescriber: v2_0167_SubstitutionNotAllowedByPrescriber,
  SubstitutionAllowedPatientRequestedProductDispensed: v2_0167_SubstitutionAllowedPatientRequestedProductDispensed,
  SubstitutionAllowedPharmacistSelectedProductDispensed: v2_0167_SubstitutionAllowedPharmacistSelectedProductDispensed,
  SubstitutionAllowedGenericDrugNotInStock: v2_0167_SubstitutionAllowedGenericDrugNotInStock,
  SubstitutionAllowedBrandDrugDispensedAsAGeneric: v2_0167_SubstitutionAllowedBrandDrugDispensedAsAGeneric,
  SubstitutionNotAllowedBrandDrugMandatedByLaw: v2_0167_SubstitutionNotAllowedBrandDrugMandatedByLaw,
  SubstitutionAllowedGenericDrugNotAvailableInMarketplace: v2_0167_SubstitutionAllowedGenericDrugNotAvailableInMarketplace,
  AGenericSubstitutionWasDispensed: v2_0167_AGenericSubstitutionWasDispensed,
  NoSubstituteWasDispensedThisIsEquivalentToTheDefaultNullValue: v2_0167_NoSubstituteWasDispensedThisIsEquivalentToTheDefaultNullValue,
  ATherapeuticSubstitutionWasDispensed: v2_0167_ATherapeuticSubstitutionWasDispensed,
};
const v2_0168_AsSoonAsPossibleAPriorityLowerThanStat: Coding = {
  code: 'A',
  display: 'As soon as possible (a priority lower than stat)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0168',
};
const v2_0168_DoAtBedsideOrPortableMayBeUsedWithOtherCodes: Coding = {
  code: 'B',
  display: 'Do at bedside or portable (may be used with other codes)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0168',
};
const v2_0168_MeasureContinuouslyEGArterialLineBloodPressure: Coding = {
  code: 'C',
  display: 'Measure continuously (e.g., arterial line blood pressure)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0168',
};
const v2_0168_PreoperativeToBeDonePriorToSurgery: Coding = {
  code: 'P',
  display: 'Preoperative (to be done prior to surgery)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0168',
};
const v2_0168_Routine: Coding = {
  code: 'R',
  display: 'Routine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0168',
};
const v2_0168_StatDoImmediately: Coding = {
  code: 'S',
  display: 'Stat (do immediately)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0168',
};
const v2_0168_TimingCriticalDoAsNearAsPossibleToRequestedTime: Coding = {
  code: 'T',
  display: 'Timing critical (do as near as possible to requested time)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0168',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0168 ( PROCESSING PRIORITY)
 */
export const V20168 = {
  AsSoonAsPossibleAPriorityLowerThanStat: v2_0168_AsSoonAsPossibleAPriorityLowerThanStat,
  DoAtBedsideOrPortableMayBeUsedWithOtherCodes: v2_0168_DoAtBedsideOrPortableMayBeUsedWithOtherCodes,
  MeasureContinuouslyEGArterialLineBloodPressure: v2_0168_MeasureContinuouslyEGArterialLineBloodPressure,
  PreoperativeToBeDonePriorToSurgery: v2_0168_PreoperativeToBeDonePriorToSurgery,
  Routine: v2_0168_Routine,
  StatDoImmediately: v2_0168_StatDoImmediately,
  TimingCriticalDoAsNearAsPossibleToRequestedTime: v2_0168_TimingCriticalDoAsNearAsPossibleToRequestedTime,
};
const v2_0169_CallBackResults: Coding = {
  code: 'C',
  display: 'Call back results',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0169',
};
const v2_0169_RushReporting: Coding = {
  code: 'R',
  display: 'Rush reporting',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0169',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0169 ( REPORTIN PRIORITY)
 */
export const V20169 = {
  CallBackResults: v2_0169_CallBackResults,
  RushReporting: v2_0169_RushReporting,
};
const v2_0170_ChildObservation: Coding = {
  code: 'C',
  display: 'Child Observation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0170',
};
const v2_0170_NotApplicable: Coding = {
  code: 'N',
  display: 'Not Applicable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0170',
};
const v2_0170_ParentObservation: Coding = {
  code: 'P',
  display: 'Parent Observation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0170',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0170 ( DERIVED SPECIMEN)
 */
export const V20170 = {
  ChildObservation: v2_0170_ChildObservation,
  NotApplicable: v2_0170_NotApplicable,
  ParentObservation: v2_0170_ParentObservation,
};
const v2_0173_Coordination: Coding = {
  code: 'CO',
  display: 'Coordination',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0173',
};
const v2_0173_Independent: Coding = {
  code: 'IN',
  display: 'Independent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0173',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0173 ( COORDINATION OF BENEFITS)
 */
export const V20173 = {
  Coordination: v2_0173_Coordination,
  Independent: v2_0173_Independent,
};
const v2_0174_AtomicServiceTestObservationTestCodeOrTreatmentCode: Coding = {
  code: 'A',
  display: 'Atomic service/test/observation (test code or treatment code)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0174',
};
const v2_0174_SingleObservationCalculatedViaARuleOrFormulaFromOtherIndependentObservationsEGAlveolarArterialRatioCardiacOutput: Coding = {
  code: 'C',
  display:
    'Single observation calculated via a rule or formula from other independent observations (e.g., Alveolar-arterial ratio, cardiac output)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0174',
};
const v2_0174_FunctionalProcedureThatMayConsistOfOneOrMoreInterrelatedMeasuresEGGlucoseToleranceTestCreatinineClearanceUsuallyDoneAtDifferentTimesAndOrOnDifferentSpecimens: Coding = {
  code: 'F',
  display:
    'Functional procedure that may consist of one or more interrelated measures (e.g., glucose tolerance test, creatinine clearance), usually done at different times and/or on different specimens',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0174',
};
const v2_0174_ProfileOrBatteryConsistingOfManyIndependentAtomicObservationsEGSMA12ElectrolytesUsuallyDoneAtOneInstrumentOnOneSpecimen: Coding = {
  code: 'P',
  display:
    'Profile or battery consisting of many independent atomic observations (e.g., SMA12, electrolytes), usually done at one instrument on one specimen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0174',
};
const v2_0174_SupersetASetOfBatteriesOrProceduresOrderedUnderASingleCodeUnitButProcessedAsSeparateBatteriesEGRoutinesEqualsCBCUAElectrolytesLessThanPGreaterThanThisSetIndicatesThatTheCodeBeingDescribedIsUsedToOrderMultipleServiceTestObservationB: Coding = {
  code: 'S',
  display:
    'Superset-a set of batteries or procedures ordered under a single code unit but processed as separate batteries (e.g., routines = CBC, UA, electrolytes)<p>This set indicates that the code being described is used to order multiple service/test/observation b',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0174',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0174 ( NATURE OF TEST/OBSERVATION)
 */
export const V20174 = {
  AtomicServiceTestObservationTestCodeOrTreatmentCode: v2_0174_AtomicServiceTestObservationTestCodeOrTreatmentCode,
  SingleObservationCalculatedViaARuleOrFormulaFromOtherIndependentObservationsEGAlveolarArterialRatioCardiacOutput: v2_0174_SingleObservationCalculatedViaARuleOrFormulaFromOtherIndependentObservationsEGAlveolarArterialRatioCardiacOutput,
  FunctionalProcedureThatMayConsistOfOneOrMoreInterrelatedMeasuresEGGlucoseToleranceTestCreatinineClearanceUsuallyDoneAtDifferentTimesAndOrOnDifferentSpecimens: v2_0174_FunctionalProcedureThatMayConsistOfOneOrMoreInterrelatedMeasuresEGGlucoseToleranceTestCreatinineClearanceUsuallyDoneAtDifferentTimesAndOrOnDifferentSpecimens,
  ProfileOrBatteryConsistingOfManyIndependentAtomicObservationsEGSMA12ElectrolytesUsuallyDoneAtOneInstrumentOnOneSpecimen: v2_0174_ProfileOrBatteryConsistingOfManyIndependentAtomicObservationsEGSMA12ElectrolytesUsuallyDoneAtOneInstrumentOnOneSpecimen,
  SupersetASetOfBatteriesOrProceduresOrderedUnderASingleCodeUnitButProcessedAsSeparateBatteriesEGRoutinesEqualsCBCUAElectrolytesLessThanPGreaterThanThisSetIndicatesThatTheCodeBeingDescribedIsUsedToOrderMultipleServiceTestObservationB: v2_0174_SupersetASetOfBatteriesOrProceduresOrderedUnderASingleCodeUnitButProcessedAsSeparateBatteriesEGRoutinesEqualsCBCUAElectrolytesLessThanPGreaterThanThisSetIndicatesThatTheCodeBeingDescribedIsUsedToOrderMultipleServiceTestObservationB,
};
const v2_0175_ChargeDescriptionMasterFile: Coding = {
  code: 'CDM',
  display: 'Charge description master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_ClinicMasterFile: Coding = {
  code: 'CLN',
  display: 'Clinic master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_ClinicalStudyWithPhasesAndScheduledMasterFile: Coding = {
  code: 'CMA',
  display: 'Clinical study with phases and scheduled master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_ClinicalStudyWithoutPhasesButWithScheduledMasterFile: Coding = {
  code: 'CMB',
  display: 'Clinical study without phases but with scheduled master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_InventoryMasterFile: Coding = {
  code: 'INV',
  display: 'Inventory master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_LocationMasterFile: Coding = {
  code: 'LOC',
  display: 'Location master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_MedicareApprovedCoverageProcess: Coding = {
  code: 'MACP',
  display: 'Medicare Approved Coverage Process',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_MedicareLimitedCoverageProcess: Coding = {
  code: 'MLCP',
  display: 'Medicare Limited Coverage Process',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_NumericalObservationMasterFile: Coding = {
  code: 'OMA',
  display: 'Numerical observation master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_CategoricalObservationMasterFile: Coding = {
  code: 'OMB',
  display: 'Categorical observation master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_ObservationBatteriesMasterFile: Coding = {
  code: 'OMC',
  display: 'Observation batteries master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_CalculatedObservationsMasterFile: Coding = {
  code: 'OMD',
  display: 'Calculated observations master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_OtherObservationServiceItemMasterFile: Coding = {
  code: 'OME',
  display: 'Other Observation/Service Item master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_MixedTypeObservationMasterFile: Coding = {
  code: 'OMM',
  display: 'Mixed type observation master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_PractitionerMasterFile: Coding = {
  code: 'PRA',
  display: 'Practitioner master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
const v2_0175_StaffMasterFile: Coding = {
  code: 'STF',
  display: 'Staff master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0175',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0175 ( MASTER FILE IDENTIFIER CODE)
 */
export const V20175 = {
  ChargeDescriptionMasterFile: v2_0175_ChargeDescriptionMasterFile,
  ClinicMasterFile: v2_0175_ClinicMasterFile,
  ClinicalStudyWithPhasesAndScheduledMasterFile: v2_0175_ClinicalStudyWithPhasesAndScheduledMasterFile,
  ClinicalStudyWithoutPhasesButWithScheduledMasterFile: v2_0175_ClinicalStudyWithoutPhasesButWithScheduledMasterFile,
  InventoryMasterFile: v2_0175_InventoryMasterFile,
  LocationMasterFile: v2_0175_LocationMasterFile,
  MedicareApprovedCoverageProcess: v2_0175_MedicareApprovedCoverageProcess,
  MedicareLimitedCoverageProcess: v2_0175_MedicareLimitedCoverageProcess,
  NumericalObservationMasterFile: v2_0175_NumericalObservationMasterFile,
  CategoricalObservationMasterFile: v2_0175_CategoricalObservationMasterFile,
  ObservationBatteriesMasterFile: v2_0175_ObservationBatteriesMasterFile,
  CalculatedObservationsMasterFile: v2_0175_CalculatedObservationsMasterFile,
  OtherObservationServiceItemMasterFile: v2_0175_OtherObservationServiceItemMasterFile,
  MixedTypeObservationMasterFile: v2_0175_MixedTypeObservationMasterFile,
  PractitionerMasterFile: v2_0175_PractitionerMasterFile,
  StaffMasterFile: v2_0175_StaffMasterFile,
};
const v2_0177_AIDSPatient: Coding = {
  code: 'AID',
  display: 'AIDS patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0177',
};
const v2_0177_Employee: Coding = {
  code: 'EMP',
  display: 'Employee',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0177',
};
const v2_0177_AlcoholDrugTreatmentPatient: Coding = {
  code: 'ETH',
  display: 'Alcohol/drug treatment patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0177',
};
const v2_0177_HIVPlusPatient: Coding = {
  code: 'HIV',
  display: 'HIV(+) patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0177',
};
const v2_0177_PsychiatricPatient: Coding = {
  code: 'PSY',
  display: 'Psychiatric patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0177',
};
const v2_0177_Restricted: Coding = {
  code: 'R',
  display: 'Restricted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0177',
};
const v2_0177_UsualControl: Coding = {
  code: 'U',
  display: 'Usual control',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0177',
};
const v2_0177_UnwedMother: Coding = {
  code: 'UWM',
  display: 'Unwed mother',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0177',
};
const v2_0177_VeryRestricted: Coding = {
  code: 'V',
  display: 'Very restricted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0177',
};
const v2_0177_VeryImportantPersonOrCelebrity: Coding = {
  code: 'VIP',
  display: 'Very important person or celebrity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0177',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0177 ( CONFIDENTIALITY CODE)
 */
export const V20177 = {
  AIDSPatient: v2_0177_AIDSPatient,
  Employee: v2_0177_Employee,
  AlcoholDrugTreatmentPatient: v2_0177_AlcoholDrugTreatmentPatient,
  HIVPlusPatient: v2_0177_HIVPlusPatient,
  PsychiatricPatient: v2_0177_PsychiatricPatient,
  Restricted: v2_0177_Restricted,
  UsualControl: v2_0177_UsualControl,
  UnwedMother: v2_0177_UnwedMother,
  VeryRestricted: v2_0177_VeryRestricted,
  VeryImportantPersonOrCelebrity: v2_0177_VeryImportantPersonOrCelebrity,
};
const v2_0178_REP: Coding = {
  code: 'REP',
  display: 'Replace current version of this master file with the version contained in this message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0178',
};
const v2_0178_UPD: Coding = {
  code: 'UPD',
  display: 'Change file records as defined in the record-level event codes for each record that follows',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0178',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0178 ( FILE-LEVEL EVENT CODE)
 */
export const V20178 = {
  REP: v2_0178_REP,
  UPD: v2_0178_UPD,
};
const v2_0179_AlwaysAllMFASegmentsWhetherDenotingErrorsOrNotMustBeReturnedViaTheApplicationLevelAcknowledgmentMessage: Coding = {
  code: 'AL',
  display: 'Always.  All MFA segments (whether denoting errors or not) must be returned via the application-level acknowledgment message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0179',
};
const v2_0179_ErrorRejectConditionsOnlyOnlyMFASegmentsDenotingErrorsMustBeReturnedViaTheApplicationLevelAcknowledgmentForThisMessage: Coding = {
  code: 'ER',
  display:
    'Error/Reject conditions only.  Only MFA segments denoting errors must be returned via the application-level acknowledgment for this message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0179',
};
const v2_0179_NeverNoApplicationLevelResponseNeeded: Coding = {
  code: 'NE',
  display: 'Never.  No application-level response needed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0179',
};
const v2_0179_SuccessOnlyMFASegmentsDenotingSuccessMustBeReturnedViaTheApplicationLevelAcknowledgmentForThisMessage: Coding = {
  code: 'SU',
  display: 'Success.  Only MFA segments denoting success must be returned via the application-level acknowledgment for this message',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0179',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0179 ( Response Level)
 */
export const V20179 = {
  AlwaysAllMFASegmentsWhetherDenotingErrorsOrNotMustBeReturnedViaTheApplicationLevelAcknowledgmentMessage: v2_0179_AlwaysAllMFASegmentsWhetherDenotingErrorsOrNotMustBeReturnedViaTheApplicationLevelAcknowledgmentMessage,
  ErrorRejectConditionsOnlyOnlyMFASegmentsDenotingErrorsMustBeReturnedViaTheApplicationLevelAcknowledgmentForThisMessage: v2_0179_ErrorRejectConditionsOnlyOnlyMFASegmentsDenotingErrorsMustBeReturnedViaTheApplicationLevelAcknowledgmentForThisMessage,
  NeverNoApplicationLevelResponseNeeded: v2_0179_NeverNoApplicationLevelResponseNeeded,
  SuccessOnlyMFASegmentsDenotingSuccessMustBeReturnedViaTheApplicationLevelAcknowledgmentForThisMessage: v2_0179_SuccessOnlyMFASegmentsDenotingSuccessMustBeReturnedViaTheApplicationLevelAcknowledgmentForThisMessage,
};
const v2_0180_ReactivateDeactivatedRecord: Coding = {
  code: 'MAC',
  display: 'Reactivate deactivated record',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0180',
};
const v2_0180_AddRecordToMasterFile: Coding = {
  code: 'MAD',
  display: 'Add record to master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0180',
};
const v2_0180_DeactivateDiscontinueUsingRecordInMasterFileButDoNotDeleteFromDatabase: Coding = {
  code: 'MDC',
  display: 'Deactivate: discontinue using record in master file, but do not delete from database',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0180',
};
const v2_0180_DeleteRecordFromMasterFile: Coding = {
  code: 'MDL',
  display: 'Delete record from master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0180',
};
const v2_0180_UpdateRecordForMasterFile: Coding = {
  code: 'MUP',
  display: 'Update record for master file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0180',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0180 ( REcord Level Event Code)
 */
export const V20180 = {
  ReactivateDeactivatedRecord: v2_0180_ReactivateDeactivatedRecord,
  AddRecordToMasterFile: v2_0180_AddRecordToMasterFile,
  DeactivateDiscontinueUsingRecordInMasterFileButDoNotDeleteFromDatabase: v2_0180_DeactivateDiscontinueUsingRecordInMasterFileButDoNotDeleteFromDatabase,
  DeleteRecordFromMasterFile: v2_0180_DeleteRecordFromMasterFile,
  UpdateRecordForMasterFile: v2_0180_UpdateRecordForMasterFile,
};
const v2_0181_SuccessfulPostingOfTheRecordDefinedByTheMFESegment: Coding = {
  code: 'S',
  display: 'Successful posting of the record defined by the MFE segment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0181',
};
const v2_0181_UnsuccessfulPostingOfTheRecordDefinedByTheMFESegment: Coding = {
  code: 'U',
  display: 'Unsuccessful posting of the record defined by the MFE segment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0181',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0181 ( MFN Recode-Level Error Return)
 */
export const V20181 = {
  SuccessfulPostingOfTheRecordDefinedByTheMFESegment: v2_0181_SuccessfulPostingOfTheRecordDefinedByTheMFESegment,
  UnsuccessfulPostingOfTheRecordDefinedByTheMFESegment: v2_0181_UnsuccessfulPostingOfTheRecordDefinedByTheMFESegment,
};
const v2_0183_ActiveStaff: Coding = {
  code: 'A',
  display: 'Active Staff',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0183',
};
const v2_0183_InactiveStaff: Coding = {
  code: 'I',
  display: 'Inactive Staff',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0183',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0183 ( Active/Inactive)
 */
export const V20183 = {
  ActiveStaff: v2_0183_ActiveStaff,
  InactiveStaff: v2_0183_InactiveStaff,
};
const v2_0185_BeeperNumber: Coding = {
  code: 'B',
  display: 'Beeper Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0185',
};
const v2_0185_CellularPhoneNumber: Coding = {
  code: 'C',
  display: 'Cellular Phone Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0185',
};
const v2_0185_EMailAddressForBackwardCompatibility: Coding = {
  code: 'E',
  display: 'E-Mail Address (for backward compatibility)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0185',
};
const v2_0185_FAXNumber: Coding = {
  code: 'F',
  display: 'FAX Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0185',
};
const v2_0185_HomePhoneNumber: Coding = {
  code: 'H',
  display: 'Home Phone Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0185',
};
const v2_0185_OfficePhoneNumber: Coding = {
  code: 'O',
  display: 'Office Phone Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0185',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0185 ( Preferred Method Of Contrct)
 */
export const V20185 = {
  BeeperNumber: v2_0185_BeeperNumber,
  CellularPhoneNumber: v2_0185_CellularPhoneNumber,
  EMailAddressForBackwardCompatibility: v2_0185_EMailAddressForBackwardCompatibility,
  FAXNumber: v2_0185_FAXNumber,
  HomePhoneNumber: v2_0185_HomePhoneNumber,
  OfficePhoneNumber: v2_0185_OfficePhoneNumber,
};
const v2_0187_InstitutionBillsForProvider: Coding = {
  code: 'I',
  display: 'Institution bills for provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0187',
};
const v2_0187_ProviderDoesOwnBilling: Coding = {
  code: 'P',
  display: 'Provider does own billing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0187',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0187 ( Provider billing)
 */
export const V20187 = {
  InstitutionBillsForProvider: v2_0187_InstitutionBillsForProvider,
  ProviderDoesOwnBilling: v2_0187_ProviderDoesOwnBilling,
};
const v2_0189_HispanicOrLatino: Coding = {
  code: 'H',
  display: 'Hispanic or Latino',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0189',
};
const v2_0189_NotHispanicOrLatino: Coding = {
  code: 'N',
  display: 'Not Hispanic or Latino',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0189',
};
const v2_0189_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0189',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0189 ( Ethnic group)
 */
export const V20189 = {
  HispanicOrLatino: v2_0189_HispanicOrLatino,
  NotHispanicOrLatino: v2_0189_NotHispanicOrLatino,
  Unknown: v2_0189_Unknown,
};
const v2_0190_FirmBusiness: Coding = {
  code: 'B',
  display: 'Firm/Business',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_BadAddress: Coding = {
  code: 'BA',
  display: 'Bad address',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_BirthDeliveryLocationAddressWhereBirthOccurred: Coding = {
  code: 'BDL',
  display: 'Birth delivery location  (address where birth occurred)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_BillingAddress: Coding = {
  code: 'BI',
  display: 'Billing Address',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_ResidenceAtBirthHomeAddressAtTimeOfBirth: Coding = {
  code: 'BR',
  display: 'Residence at birth (home address at time of birth)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_CurrentOrTemporary: Coding = {
  code: 'C',
  display: 'Current Or Temporary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_CountryOfOrigin: Coding = {
  code: 'F',
  display: 'Country Of Origin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_Home: Coding = {
  code: 'H',
  display: 'Home',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_LegalAddress: Coding = {
  code: 'L',
  display: 'Legal Address',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_Mailing: Coding = {
  code: 'M',
  display: 'Mailing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_BirthNeeBirthAddressNotOtherwiseSpecified: Coding = {
  code: 'N',
  display: 'Birth (nee)  (birth address, not otherwise specified)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_OfficeBusiness: Coding = {
  code: 'O',
  display: 'Office/Business',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_Permanent: Coding = {
  code: 'P',
  display: 'Permanent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_RegistryHomeRefersToTheInformationSystemTypicallyManagedByAPublicHealthAgencyThatStoresPatientInformationSuchAsImmunizationHistoriesOrCancerDataRegardlessOfWhereThePatientObtainsServices: Coding = {
  code: 'RH',
  display:
    'Registry home. Refers to the information system, typically managed by a public health agency, that stores patient information such as immunization histories or cancer data, regardless of where the patient obtains services.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_ServiceLocation: Coding = {
  code: 'S',
  display: 'Service Location',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_ShippingAddress: Coding = {
  code: 'SH',
  display: 'Shipping Address',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_TubeAddress: Coding = {
  code: 'TM',
  display: 'Tube Address',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
const v2_0190_Vacation: Coding = {
  code: 'V',
  display: 'Vacation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0190',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0190 ( Adress Type)
 */
export const V20190 = {
  FirmBusiness: v2_0190_FirmBusiness,
  BadAddress: v2_0190_BadAddress,
  BirthDeliveryLocationAddressWhereBirthOccurred: v2_0190_BirthDeliveryLocationAddressWhereBirthOccurred,
  BillingAddress: v2_0190_BillingAddress,
  ResidenceAtBirthHomeAddressAtTimeOfBirth: v2_0190_ResidenceAtBirthHomeAddressAtTimeOfBirth,
  CurrentOrTemporary: v2_0190_CurrentOrTemporary,
  CountryOfOrigin: v2_0190_CountryOfOrigin,
  Home: v2_0190_Home,
  LegalAddress: v2_0190_LegalAddress,
  Mailing: v2_0190_Mailing,
  BirthNeeBirthAddressNotOtherwiseSpecified: v2_0190_BirthNeeBirthAddressNotOtherwiseSpecified,
  OfficeBusiness: v2_0190_OfficeBusiness,
  Permanent: v2_0190_Permanent,
  RegistryHomeRefersToTheInformationSystemTypicallyManagedByAPublicHealthAgencyThatStoresPatientInformationSuchAsImmunizationHistoriesOrCancerDataRegardlessOfWhereThePatientObtainsServices: v2_0190_RegistryHomeRefersToTheInformationSystemTypicallyManagedByAPublicHealthAgencyThatStoresPatientInformationSuchAsImmunizationHistoriesOrCancerDataRegardlessOfWhereThePatientObtainsServices,
  ServiceLocation: v2_0190_ServiceLocation,
  ShippingAddress: v2_0190_ShippingAddress,
  TubeAddress: v2_0190_TubeAddress,
  Vacation: v2_0190_Vacation,
};
const v2_0191_OtherApplicationDataTypicallyUninterpretedBinaryDataHL7V23AndLater: Coding = {
  code: 'AP',
  display: 'Other application data, typically uninterpreted binary data (HL7 V2.3 and later)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0191',
};
const v2_0191_AudioDataHL7V23AndLater: Coding = {
  code: 'AU',
  display: 'Audio data (HL7 V2.3 and later)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0191',
};
const v2_0191_FormattedTextHL7V22Only: Coding = {
  code: 'FT',
  display: 'Formatted text (HL7 V2.2 only)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0191',
};
const v2_0191_ImageDataHL7V23AndLater: Coding = {
  code: 'IM',
  display: 'Image data (HL7 V2.3 and later)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0191',
};
const v2_0191_MIMEMultipartPackage: Coding = {
  code: 'multipart',
  display: 'MIME multipart package',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0191',
};
const v2_0191_NonScannedImageHL7V22Only: Coding = {
  code: 'NS',
  display: 'Non-scanned image (HL7 V2.2 only)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0191',
};
const v2_0191_ScannedDocumentHL7V22Only: Coding = {
  code: 'SD',
  display: 'Scanned document (HL7 V2.2 only)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0191',
};
const v2_0191_ScannedImageHL7V22Only: Coding = {
  code: 'SI',
  display: 'Scanned image (HL7 V2.2 only)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0191',
};
const v2_0191_MachineReadableTextDocumentHL7V231AndLater: Coding = {
  code: 'TEXT',
  display: 'Machine readable text document (HL7 V2.3.1 and later)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0191',
};
const v2_0191_MachineReadableTextDocumentHL7V22Only: Coding = {
  code: 'TX',
  display: 'Machine readable text document (HL7 V2.2 only)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0191',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0191 ( Type Of Data)
 */
export const V20191 = {
  OtherApplicationDataTypicallyUninterpretedBinaryDataHL7V23AndLater: v2_0191_OtherApplicationDataTypicallyUninterpretedBinaryDataHL7V23AndLater,
  AudioDataHL7V23AndLater: v2_0191_AudioDataHL7V23AndLater,
  FormattedTextHL7V22Only: v2_0191_FormattedTextHL7V22Only,
  ImageDataHL7V23AndLater: v2_0191_ImageDataHL7V23AndLater,
  MIMEMultipartPackage: v2_0191_MIMEMultipartPackage,
  NonScannedImageHL7V22Only: v2_0191_NonScannedImageHL7V22Only,
  ScannedDocumentHL7V22Only: v2_0191_ScannedDocumentHL7V22Only,
  ScannedImageHL7V22Only: v2_0191_ScannedImageHL7V22Only,
  MachineReadableTextDocumentHL7V231AndLater: v2_0191_MachineReadableTextDocumentHL7V231AndLater,
  MachineReadableTextDocumentHL7V22Only: v2_0191_MachineReadableTextDocumentHL7V22Only,
};
const v2_0193_Amount: Coding = {
  code: 'AT',
  display: 'Amount',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0193',
};
const v2_0193_Limit: Coding = {
  code: 'LM',
  display: 'Limit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0193',
};
const v2_0193_Percentage: Coding = {
  code: 'PC',
  display: 'Percentage',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0193',
};
const v2_0193_Unlimited: Coding = {
  code: 'UL',
  display: 'Unlimited',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0193',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0193 ( Amount Class)
 */
export const V20193 = {
  Amount: v2_0193_Amount,
  Limit: v2_0193_Limit,
  Percentage: v2_0193_Percentage,
  Unlimited: v2_0193_Unlimited,
};
const v2_0200_Assigned: Coding = {
  code: 'A',
  display: 'Assigned',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_BirthName: Coding = {
  code: 'B',
  display: 'Birth name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_BadName: Coding = {
  code: 'BAD',
  display: 'Bad Name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_AdoptedName: Coding = {
  code: 'C',
  display: 'Adopted Name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_CustomaryName: Coding = {
  code: 'D',
  display: 'Customary Name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_FathersName: Coding = {
  code: 'F',
  display: 'Fathers Name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_LicensingName: Coding = {
  code: 'I',
  display: 'Licensing Name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_BusinessName: Coding = {
  code: 'K',
  display: 'Business name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_OfficialRegistryName: Coding = {
  code: 'L',
  display: 'Official Registry Name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_MaidenName: Coding = {
  code: 'M',
  display: 'Maiden Name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_Masked: Coding = {
  code: 'MSK',
  display: 'Masked',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_Nickname: Coding = {
  code: 'N',
  display: 'Nickname',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_TemporarilyUnavailable: Coding = {
  code: 'NAV',
  display: 'Temporarily Unavailable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_NewbornName: Coding = {
  code: 'NB',
  display: 'Newborn Name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_NoLongerToBeUsed: Coding = {
  code: 'NOUSE',
  display: 'No Longer To Be Used',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_NameOfPartnerSpouse: Coding = {
  code: 'P',
  display: 'Name of Partner/Spouse',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_RegisteredName: Coding = {
  code: 'R',
  display: 'Registered Name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_Religious: Coding = {
  code: 'REL',
  display: 'Religious',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_Pseudonym: Coding = {
  code: 'S',
  display: 'Pseudonym',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_IndigenousTribal: Coding = {
  code: 'T',
  display: 'Indigenous/Tribal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_TemporaryName: Coding = {
  code: 'TEMP',
  display: 'Temporary Name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
const v2_0200_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0200',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0200 ( Name Type)
 */
export const V20200 = {
  Assigned: v2_0200_Assigned,
  BirthName: v2_0200_BirthName,
  BadName: v2_0200_BadName,
  AdoptedName: v2_0200_AdoptedName,
  CustomaryName: v2_0200_CustomaryName,
  FathersName: v2_0200_FathersName,
  LicensingName: v2_0200_LicensingName,
  BusinessName: v2_0200_BusinessName,
  OfficialRegistryName: v2_0200_OfficialRegistryName,
  MaidenName: v2_0200_MaidenName,
  Masked: v2_0200_Masked,
  Nickname: v2_0200_Nickname,
  TemporarilyUnavailable: v2_0200_TemporarilyUnavailable,
  NewbornName: v2_0200_NewbornName,
  NoLongerToBeUsed: v2_0200_NoLongerToBeUsed,
  NameOfPartnerSpouse: v2_0200_NameOfPartnerSpouse,
  RegisteredName: v2_0200_RegisteredName,
  Religious: v2_0200_Religious,
  Pseudonym: v2_0200_Pseudonym,
  IndigenousTribal: v2_0200_IndigenousTribal,
  TemporaryName: v2_0200_TemporaryName,
  Unknown: v2_0200_Unknown,
};
const v2_0201_AnsweringServiceNumber: Coding = {
  code: 'ASN',
  display: 'Answering Service Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0201',
};
const v2_0201_BeeperNumber: Coding = {
  code: 'BPN',
  display: 'Beeper Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0201',
};
const v2_0201_EmergencyNumber: Coding = {
  code: 'EMR',
  display: 'Emergency Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0201',
};
const v2_0201_NetworkEmailAddress: Coding = {
  code: 'NET',
  display: 'Network (email) Address',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0201',
};
const v2_0201_OtherResidenceNumber: Coding = {
  code: 'ORN',
  display: 'Other Residence Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0201',
};
const v2_0201_PrimaryResidenceNumber: Coding = {
  code: 'PRN',
  display: 'Primary Residence Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0201',
};
const v2_0201_Personal: Coding = {
  code: 'PRS',
  display: 'Personal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0201',
};
const v2_0201_VacationHomeNumber: Coding = {
  code: 'VHN',
  display: 'Vacation Home Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0201',
};
const v2_0201_WorkNumber: Coding = {
  code: 'WPN',
  display: 'Work Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0201',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0201 ( Telecommunication Use Code)
 */
export const V20201 = {
  AnsweringServiceNumber: v2_0201_AnsweringServiceNumber,
  BeeperNumber: v2_0201_BeeperNumber,
  EmergencyNumber: v2_0201_EmergencyNumber,
  NetworkEmailAddress: v2_0201_NetworkEmailAddress,
  OtherResidenceNumber: v2_0201_OtherResidenceNumber,
  PrimaryResidenceNumber: v2_0201_PrimaryResidenceNumber,
  Personal: v2_0201_Personal,
  VacationHomeNumber: v2_0201_VacationHomeNumber,
  WorkNumber: v2_0201_WorkNumber,
};
const v2_0202_Beeper: Coding = {
  code: 'BP',
  display: 'Beeper',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0202',
};
const v2_0202_CellularOrMobilePhone: Coding = {
  code: 'CP',
  display: 'Cellular or Mobile Phone',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0202',
};
const v2_0202_Fax: Coding = {
  code: 'FX',
  display: 'Fax',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0202',
};
const v2_0202_InternetAddress: Coding = {
  code: 'Internet',
  display: 'Internet Address',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0202',
};
const v2_0202_Modem: Coding = {
  code: 'MD',
  display: 'Modem',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0202',
};
const v2_0202_Telephone: Coding = {
  code: 'PH',
  display: 'Telephone',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0202',
};
const v2_0202_SatellitePhone: Coding = {
  code: 'SAT',
  display: 'Satellite Phone',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0202',
};
const v2_0202_TelecommunicationsDeviceForTheDeaf: Coding = {
  code: 'TDD',
  display: 'Telecommunications Device for the Deaf',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0202',
};
const v2_0202_Teletypewriter: Coding = {
  code: 'TTY',
  display: 'Teletypewriter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0202',
};
const v2_0202_X400EmailAddress: Coding = {
  code: 'X.400',
  display: 'X.400 email address',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0202',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0202 ( Telecommunication Equipment Type)
 */
export const V20202 = {
  Beeper: v2_0202_Beeper,
  CellularOrMobilePhone: v2_0202_CellularOrMobilePhone,
  Fax: v2_0202_Fax,
  InternetAddress: v2_0202_InternetAddress,
  Modem: v2_0202_Modem,
  Telephone: v2_0202_Telephone,
  SatellitePhone: v2_0202_SatellitePhone,
  TelecommunicationsDeviceForTheDeaf: v2_0202_TelecommunicationsDeviceForTheDeaf,
  Teletypewriter: v2_0202_Teletypewriter,
  X400EmailAddress: v2_0202_X400EmailAddress,
};
const v2_0203_AccreditationCertificationIdentifier: Coding = {
  code: 'AC',
  display: 'Accreditation/Certification Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_AmericanExpress: Coding = {
  code: 'AM',
  display: 'American Express',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_AmericanMedicalAssociationNumber: Coding = {
  code: 'AMA',
  display: 'American Medical Association Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_AccountNumber: Coding = {
  code: 'AN',
  display: 'Account number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_AccountNumberCreditor: Coding = {
  code: 'ANC',
  display: 'Account number Creditor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_AccountNumberDebitor: Coding = {
  code: 'AND',
  display: 'Account number debitor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_AnonymousIdentifier: Coding = {
  code: 'ANON',
  display: 'Anonymous identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_TemporaryAccountNumber: Coding = {
  code: 'ANT',
  display: 'Temporary Account Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_AdvancedPracticeRegisteredNurseNumber: Coding = {
  code: 'APRN',
  display: 'Advanced Practice Registered Nurse number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_AncestorSpecimenID: Coding = {
  code: 'ASID',
  display: 'Ancestor Specimen ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_BankAccountNumber: Coding = {
  code: 'BA',
  display: 'Bank Account Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_BankCardNumber: Coding = {
  code: 'BC',
  display: 'Bank Card Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_BirthCertificateFileNumber: Coding = {
  code: 'BCFN',
  display: 'Birth Certificate File Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_BirthCertificate: Coding = {
  code: 'BCT',
  display: 'Birth Certificate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_BirthRegistryNumber: Coding = {
  code: 'BR',
  display: 'Birth registry number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PrimaryPhysicianOfficeNumber: Coding = {
  code: 'BSNR',
  display: 'Primary physician office number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_CostCenterNumber: Coding = {
  code: 'CC',
  display: 'Cost Center number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_ChangeOfNameDocument: Coding = {
  code: 'CONM',
  display: 'Change of Name Document',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_CountyNumber: Coding = {
  code: 'CY',
  display: 'County number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_CitizenshipCard: Coding = {
  code: 'CZ',
  display: 'Citizenship Card',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_DeathCertificateID: Coding = {
  code: 'DC',
  display: 'Death Certificate ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_DeathCertificateFileNumber: Coding = {
  code: 'DCFN',
  display: 'Death Certificate File Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_DentistLicenseNumber: Coding = {
  code: 'DDS',
  display: 'Dentist license number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_DrugEnforcementAdministrationRegistrationNumber: Coding = {
  code: 'DEA',
  display: 'Drug Enforcement Administration registration number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_DrugFurnishingOrPrescriptiveAuthorityNumber: Coding = {
  code: 'DFN',
  display: 'Drug Furnishing or prescriptive authority Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_DinerQuoteSClubCard: Coding = {
  code: 'DI',
  display: "Diner's Club card",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_DoctorNumber: Coding = {
  code: 'DN',
  display: 'Doctor number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_OsteopathicLicenseNumber: Coding = {
  code: 'DO',
  display: 'Osteopathic License number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_DiplomaticPassport: Coding = {
  code: 'DP',
  display: 'Diplomatic Passport',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PodiatristLicenseNumber: Coding = {
  code: 'DPM',
  display: 'Podiatrist license number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_DiscoverCard: Coding = {
  code: 'DS',
  display: 'Discover Card',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_EmployeeNumber: Coding = {
  code: 'EI',
  display: 'Employee number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_StaffEnterpriseNumber: Coding = {
  code: 'ESN',
  display: 'Staff Enterprise Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_FetalDeathReportID: Coding = {
  code: 'FDR',
  display: 'Fetal Death Report ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_FetalDeathReportFileNumber: Coding = {
  code: 'FDRFN',
  display: 'Fetal Death Report File Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_FacilityID: Coding = {
  code: 'FI',
  display: 'Facility ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_GuarantorInternalIdentifier: Coding = {
  code: 'GI',
  display: 'Guarantor internal identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_GeneralLedgerNumber: Coding = {
  code: 'GL',
  display: 'General ledger number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_GuarantorExternalIdentifier: Coding = {
  code: 'GN',
  display: 'Guarantor external  identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_HealthCardNumber: Coding = {
  code: 'HC',
  display: 'Health Card Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_IndigenousAboriginal: Coding = {
  code: 'IND',
  display: 'Indigenous/Aboriginal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_LaboratoryAccessionID: Coding = {
  code: 'LACSN',
  display: 'Laboratory Accession ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_LifelongPhysicianNumber: Coding = {
  code: 'LANR',
  display: 'Lifelong physician number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_LaborAndIndustriesNumber: Coding = {
  code: 'LI',
  display: 'Labor and industries number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_LicenseNumber: Coding = {
  code: 'LN',
  display: 'License number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_LocalRegistryID: Coding = {
  code: 'LR',
  display: 'Local Registry ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PatientMedicaidNumber: Coding = {
  code: 'MA',
  display: 'Patient Medicaid number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_MemberNumber: Coding = {
  code: 'MB',
  display: 'Member Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PatientQuoteSMedicareNumber: Coding = {
  code: 'MC',
  display: "Patient's Medicare number",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PractitionerMedicaidNumber: Coding = {
  code: 'MCD',
  display: 'Practitioner Medicaid number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PractitionerMedicareNumber: Coding = {
  code: 'MCR',
  display: 'Practitioner Medicare number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_MarriageCertificate: Coding = {
  code: 'MCT',
  display: 'Marriage Certificate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_MilitaryIDNumber: Coding = {
  code: 'MI',
  display: 'Military ID number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_TemporaryMedicalRecordNumber: Coding = {
  code: 'MRT',
  display: 'Temporary Medical Record Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_MasterCard: Coding = {
  code: 'MS',
  display: 'MasterCard',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_SecondaryPhysicianOfficeNumber: Coding = {
  code: 'NBSNR',
  display: 'Secondary physician office number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_NaturalizationCertificate: Coding = {
  code: 'NCT',
  display: 'Naturalization Certificate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_NationalEmployerIdentifier: Coding = {
  code: 'NE',
  display: 'National employer identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_NationalHealthPlanIdentifier: Coding = {
  code: 'NH',
  display: 'National Health Plan Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_NationalUniqueIndividualIdentifier: Coding = {
  code: 'NI',
  display: 'National unique individual identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_NationalInsuranceOrganizationIdentifier: Coding = {
  code: 'NII',
  display: 'National Insurance Organization Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_NationalPersonIdentifierWhereTheXxxIsTheISOTable31663CharacterAlphabeticCountryCode: Coding = {
  code: 'NNxxx',
  display: 'National Person Identifier where the xxx is the ISO table 3166 3-character (alphabetic) country code',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_NursePractitionerNumber: Coding = {
  code: 'NP',
  display: 'Nurse practitioner number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_NationalProviderIdentifier: Coding = {
  code: 'NPI',
  display: 'National provider identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_ObservationInstanceIdentifier: Coding = {
  code: 'OBI',
  display: 'Observation Instance Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_OptometristLicenseNumber: Coding = {
  code: 'OD',
  display: 'Optometrist license number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PhysicianAssistantNumber: Coding = {
  code: 'PA',
  display: 'Physician Assistant number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_ParoleCard: Coding = {
  code: 'PC',
  display: 'Parole Card',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PenitentiaryCorrectionalInstitutionNumber: Coding = {
  code: 'PCN',
  display: 'Penitentiary/correctional institution Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_LivingSubjectEnterpriseNumber: Coding = {
  code: 'PE',
  display: 'Living Subject Enterprise Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PensionNumber: Coding = {
  code: 'PEN',
  display: 'Pension Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PublicHealthCaseIdentifier: Coding = {
  code: 'PHC',
  display: 'Public Health Case Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PublicHealthEventIdentifier: Coding = {
  code: 'PHE',
  display: 'Public Health Event Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PublicHealthOfficialID: Coding = {
  code: 'PHO',
  display: 'Public Health Official ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PatientInternalIdentifier: Coding = {
  code: 'PI',
  display: 'Patient internal identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PersonNumber: Coding = {
  code: 'PN',
  display: 'Person number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_TemporaryLivingSubjectNumber: Coding = {
  code: 'PNT',
  display: 'Temporary Living Subject Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_MedicareCMSPerformingProviderIdentificationNumber: Coding = {
  code: 'PPIN',
  display: 'Medicare/CMS Performing Provider Identification Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PermanentResidentCardNumber: Coding = {
  code: 'PRC',
  display: 'Permanent Resident Card Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PatientExternalIdentifier: Coding = {
  code: 'PT',
  display: 'Patient external identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_QANumber: Coding = {
  code: 'QA',
  display: 'QA number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_ResourceIdentifier: Coding = {
  code: 'RI',
  display: 'Resource identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_RegisteredNurseNumber: Coding = {
  code: 'RN',
  display: 'Registered Nurse Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_PharmacistLicenseNumber: Coding = {
  code: 'RPH',
  display: 'Pharmacist license number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_RailroadRetirementNumber: Coding = {
  code: 'RR',
  display: 'Railroad Retirement number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_RegionalRegistryID: Coding = {
  code: 'RRI',
  display: 'Regional registry ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_RailroadRetirementProvider: Coding = {
  code: 'RRP',
  display: 'Railroad Retirement Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_SpecimenID: Coding = {
  code: 'SID',
  display: 'Specimen ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_StateLicense: Coding = {
  code: 'SL',
  display: 'State license',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_SubscriberNumber: Coding = {
  code: 'SN',
  display: 'Subscriber Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_StateAssignedNDBSCardIdentifier: Coding = {
  code: 'SNBSN',
  display: 'State assigned NDBS card Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_StudyPermit: Coding = {
  code: 'SP',
  display: 'Study Permit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_StateRegistryID: Coding = {
  code: 'SR',
  display: 'State registry ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_SocialSecurityNumber: Coding = {
  code: 'SS',
  display: 'Social Security number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_ShipmentTrackingNumber: Coding = {
  code: 'STN',
  display: 'Shipment Tracking Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_TreatyNumberCanada: Coding = {
  code: 'TN',
  display: 'Treaty Number/ (Canada)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_TemporaryPermanentResidentCanada: Coding = {
  code: 'TPR',
  display: 'Temporary Permanent Resident (Canada)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_TrainingLicenseNumber: Coding = {
  code: 'TRL',
  display: 'Training License Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_UnspecifiedIdentifier: Coding = {
  code: 'U',
  display: 'Unspecified identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_MedicareCMSFormerlyHCFAQuoteSUniversalPhysicianIdentificationNumbers: Coding = {
  code: 'UPIN',
  display: "Medicare/CMS (formerly HCFA)'s Universal Physician Identification numbers",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_UniqueSpecimenID: Coding = {
  code: 'USID',
  display: 'Unique Specimen ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_VisitNumber: Coding = {
  code: 'VN',
  display: 'Visit number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_VisitorPermit: Coding = {
  code: 'VP',
  display: 'Visitor Permit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_VISA: Coding = {
  code: 'VS',
  display: 'VISA',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_WICIdentifier: Coding = {
  code: 'WC',
  display: 'WIC identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_WorkersQuoteCompNumber: Coding = {
  code: 'WCN',
  display: "Workers' Comp Number",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_WorkPermit: Coding = {
  code: 'WP',
  display: 'Work Permit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_HealthPlanIdentifier: Coding = {
  code: 'XV',
  display: 'Health Plan Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
const v2_0203_OrganizationIdentifier: Coding = {
  code: 'XX',
  display: 'Organization identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0203 ( Identifier Type)
 */
export const V20203 = {
  AccreditationCertificationIdentifier: v2_0203_AccreditationCertificationIdentifier,
  AccessionID: v2_0203_AccessionID,
  AmericanExpress: v2_0203_AmericanExpress,
  AmericanMedicalAssociationNumber: v2_0203_AmericanMedicalAssociationNumber,
  AccountNumber: v2_0203_AccountNumber,
  AccountNumberCreditor: v2_0203_AccountNumberCreditor,
  AccountNumberDebitor: v2_0203_AccountNumberDebitor,
  AnonymousIdentifier: v2_0203_AnonymousIdentifier,
  TemporaryAccountNumber: v2_0203_TemporaryAccountNumber,
  AdvancedPracticeRegisteredNurseNumber: v2_0203_AdvancedPracticeRegisteredNurseNumber,
  AncestorSpecimenID: v2_0203_AncestorSpecimenID,
  BankAccountNumber: v2_0203_BankAccountNumber,
  BankCardNumber: v2_0203_BankCardNumber,
  BirthCertificateFileNumber: v2_0203_BirthCertificateFileNumber,
  BirthCertificate: v2_0203_BirthCertificate,
  BirthRegistryNumber: v2_0203_BirthRegistryNumber,
  BreedRegistryNumber: v2_0203_BreedRegistryNumber,
  PrimaryPhysicianOfficeNumber: v2_0203_PrimaryPhysicianOfficeNumber,
  CostCenterNumber: v2_0203_CostCenterNumber,
  ChangeOfNameDocument: v2_0203_ChangeOfNameDocument,
  CountyNumber: v2_0203_CountyNumber,
  CitizenshipCard: v2_0203_CitizenshipCard,
  DeathCertificateID: v2_0203_DeathCertificateID,
  DeathCertificateFileNumber: v2_0203_DeathCertificateFileNumber,
  DentistLicenseNumber: v2_0203_DentistLicenseNumber,
  DrugEnforcementAdministrationRegistrationNumber: v2_0203_DrugEnforcementAdministrationRegistrationNumber,
  DrugFurnishingOrPrescriptiveAuthorityNumber: v2_0203_DrugFurnishingOrPrescriptiveAuthorityNumber,
  DinerQuoteSClubCard: v2_0203_DinerQuoteSClubCard,
  DriverQuoteSLicenseNumber: v2_0203_DriverQuoteSLicenseNumber,
  DoctorNumber: v2_0203_DoctorNumber,
  OsteopathicLicenseNumber: v2_0203_OsteopathicLicenseNumber,
  DiplomaticPassport: v2_0203_DiplomaticPassport,
  PodiatristLicenseNumber: v2_0203_PodiatristLicenseNumber,
  DonorRegistrationNumber: v2_0203_DonorRegistrationNumber,
  DiscoverCard: v2_0203_DiscoverCard,
  EmployeeNumber: v2_0203_EmployeeNumber,
  EmployerNumber: v2_0203_EmployerNumber,
  StaffEnterpriseNumber: v2_0203_StaffEnterpriseNumber,
  FetalDeathReportID: v2_0203_FetalDeathReportID,
  FetalDeathReportFileNumber: v2_0203_FetalDeathReportFileNumber,
  FacilityID: v2_0203_FacilityID,
  FillerIdentifier: v2_0203_FillerIdentifier,
  GuarantorInternalIdentifier: v2_0203_GuarantorInternalIdentifier,
  GeneralLedgerNumber: v2_0203_GeneralLedgerNumber,
  GuarantorExternalIdentifier: v2_0203_GuarantorExternalIdentifier,
  HealthCardNumber: v2_0203_HealthCardNumber,
  IndigenousAboriginal: v2_0203_IndigenousAboriginal,
  JurisdictionalHealthNumberCanada: v2_0203_JurisdictionalHealthNumberCanada,
  LaboratoryAccessionID: v2_0203_LaboratoryAccessionID,
  LifelongPhysicianNumber: v2_0203_LifelongPhysicianNumber,
  LaborAndIndustriesNumber: v2_0203_LaborAndIndustriesNumber,
  LicenseNumber: v2_0203_LicenseNumber,
  LocalRegistryID: v2_0203_LocalRegistryID,
  PatientMedicaidNumber: v2_0203_PatientMedicaidNumber,
  MemberNumber: v2_0203_MemberNumber,
  PatientQuoteSMedicareNumber: v2_0203_PatientQuoteSMedicareNumber,
  PractitionerMedicaidNumber: v2_0203_PractitionerMedicaidNumber,
  MicrochipNumber: v2_0203_MicrochipNumber,
  PractitionerMedicareNumber: v2_0203_PractitionerMedicareNumber,
  MarriageCertificate: v2_0203_MarriageCertificate,
  MedicalLicenseNumber: v2_0203_MedicalLicenseNumber,
  MilitaryIDNumber: v2_0203_MilitaryIDNumber,
  MedicalRecordNumber: v2_0203_MedicalRecordNumber,
  TemporaryMedicalRecordNumber: v2_0203_TemporaryMedicalRecordNumber,
  MasterCard: v2_0203_MasterCard,
  SecondaryPhysicianOfficeNumber: v2_0203_SecondaryPhysicianOfficeNumber,
  NaturalizationCertificate: v2_0203_NaturalizationCertificate,
  NationalEmployerIdentifier: v2_0203_NationalEmployerIdentifier,
  NationalHealthPlanIdentifier: v2_0203_NationalHealthPlanIdentifier,
  NationalUniqueIndividualIdentifier: v2_0203_NationalUniqueIndividualIdentifier,
  NationalInsuranceOrganizationIdentifier: v2_0203_NationalInsuranceOrganizationIdentifier,
  NationalInsurancePayorIdentifierPayor: v2_0203_NationalInsurancePayorIdentifierPayor,
  NationalPersonIdentifierWhereTheXxxIsTheISOTable31663CharacterAlphabeticCountryCode: v2_0203_NationalPersonIdentifierWhereTheXxxIsTheISOTable31663CharacterAlphabeticCountryCode,
  NursePractitionerNumber: v2_0203_NursePractitionerNumber,
  NationalProviderIdentifier: v2_0203_NationalProviderIdentifier,
  ObservationInstanceIdentifier: v2_0203_ObservationInstanceIdentifier,
  OptometristLicenseNumber: v2_0203_OptometristLicenseNumber,
  PhysicianAssistantNumber: v2_0203_PhysicianAssistantNumber,
  ParoleCard: v2_0203_ParoleCard,
  PenitentiaryCorrectionalInstitutionNumber: v2_0203_PenitentiaryCorrectionalInstitutionNumber,
  LivingSubjectEnterpriseNumber: v2_0203_LivingSubjectEnterpriseNumber,
  PensionNumber: v2_0203_PensionNumber,
  PublicHealthCaseIdentifier: v2_0203_PublicHealthCaseIdentifier,
  PublicHealthEventIdentifier: v2_0203_PublicHealthEventIdentifier,
  PublicHealthOfficialID: v2_0203_PublicHealthOfficialID,
  PatientInternalIdentifier: v2_0203_PatientInternalIdentifier,
  PlacerIdentifier: v2_0203_PlacerIdentifier,
  PersonNumber: v2_0203_PersonNumber,
  TemporaryLivingSubjectNumber: v2_0203_TemporaryLivingSubjectNumber,
  MedicareCMSPerformingProviderIdentificationNumber: v2_0203_MedicareCMSPerformingProviderIdentificationNumber,
  PassportNumber: v2_0203_PassportNumber,
  PermanentResidentCardNumber: v2_0203_PermanentResidentCardNumber,
  ProviderNumber: v2_0203_ProviderNumber,
  PatientExternalIdentifier: v2_0203_PatientExternalIdentifier,
  QANumber: v2_0203_QANumber,
  ResourceIdentifier: v2_0203_ResourceIdentifier,
  RegisteredNurseNumber: v2_0203_RegisteredNurseNumber,
  PharmacistLicenseNumber: v2_0203_PharmacistLicenseNumber,
  RailroadRetirementNumber: v2_0203_RailroadRetirementNumber,
  RegionalRegistryID: v2_0203_RegionalRegistryID,
  RailroadRetirementProvider: v2_0203_RailroadRetirementProvider,
  SocialBeneficiaryIdentifier: v2_0203_SocialBeneficiaryIdentifier,
  SpecimenID: v2_0203_SpecimenID,
  StateLicense: v2_0203_StateLicense,
  SubscriberNumber: v2_0203_SubscriberNumber,
  StateAssignedNDBSCardIdentifier: v2_0203_StateAssignedNDBSCardIdentifier,
  SerialNumber: v2_0203_SerialNumber,
  StudyPermit: v2_0203_StudyPermit,
  StateRegistryID: v2_0203_StateRegistryID,
  SocialSecurityNumber: v2_0203_SocialSecurityNumber,
  ShipmentTrackingNumber: v2_0203_ShipmentTrackingNumber,
  TaxIDNumber: v2_0203_TaxIDNumber,
  TreatyNumberCanada: v2_0203_TreatyNumberCanada,
  TemporaryPermanentResidentCanada: v2_0203_TemporaryPermanentResidentCanada,
  TrainingLicenseNumber: v2_0203_TrainingLicenseNumber,
  UnspecifiedIdentifier: v2_0203_UnspecifiedIdentifier,
  UniversalDeviceIdentifier: v2_0203_UniversalDeviceIdentifier,
  MedicareCMSFormerlyHCFAQuoteSUniversalPhysicianIdentificationNumbers: v2_0203_MedicareCMSFormerlyHCFAQuoteSUniversalPhysicianIdentificationNumbers,
  UniqueSpecimenID: v2_0203_UniqueSpecimenID,
  VisitNumber: v2_0203_VisitNumber,
  VisitorPermit: v2_0203_VisitorPermit,
  VISA: v2_0203_VISA,
  WICIdentifier: v2_0203_WICIdentifier,
  WorkersQuoteCompNumber: v2_0203_WorkersQuoteCompNumber,
  WorkPermit: v2_0203_WorkPermit,
  HealthPlanIdentifier: v2_0203_HealthPlanIdentifier,
  OrganizationIdentifier: v2_0203_OrganizationIdentifier,
};
const v2_0204_AliasName: Coding = {
  code: 'A',
  display: 'Alias name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0204',
};
const v2_0204_DisplayName: Coding = {
  code: 'D',
  display: 'Display name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0204',
};
const v2_0204_LegalName: Coding = {
  code: 'L',
  display: 'Legal name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0204',
};
const v2_0204_StockExchangeListingName: Coding = {
  code: 'SL',
  display: 'Stock exchange listing name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0204',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0204 ( Organizational Name Type)
 */
export const V20204 = {
  AliasName: v2_0204_AliasName,
  DisplayName: v2_0204_DisplayName,
  LegalName: v2_0204_LegalName,
  StockExchangeListingName: v2_0204_StockExchangeListingName,
};
const v2_0205_AdministrativePriceOrHandlingFee: Coding = {
  code: 'AP',
  display: 'administrative price or handling fee',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0205',
};
const v2_0205_DirectUnitCost: Coding = {
  code: 'DC',
  display: 'direct unit cost',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0205',
};
const v2_0205_IndirectUnitCost: Coding = {
  code: 'IC',
  display: 'indirect unit cost',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0205',
};
const v2_0205_ProfessionalFeeForPerformingProvider: Coding = {
  code: 'PF',
  display: 'professional fee for performing provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0205',
};
const v2_0205_TechnologyFeeForUseOfEquipment: Coding = {
  code: 'TF',
  display: 'technology fee for use of equipment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0205',
};
const v2_0205_TotalPrice: Coding = {
  code: 'TP',
  display: 'total price',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0205',
};
const v2_0205_UnitPriceMayBeBasedOnLengthOfProcedureOrService: Coding = {
  code: 'UP',
  display: 'unit price, may be based on length of procedure or service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0205',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0205 ( Price Type)
 */
export const V20205 = {
  AdministrativePriceOrHandlingFee: v2_0205_AdministrativePriceOrHandlingFee,
  DirectUnitCost: v2_0205_DirectUnitCost,
  IndirectUnitCost: v2_0205_IndirectUnitCost,
  ProfessionalFeeForPerformingProvider: v2_0205_ProfessionalFeeForPerformingProvider,
  TechnologyFeeForUseOfEquipment: v2_0205_TechnologyFeeForUseOfEquipment,
  TotalPrice: v2_0205_TotalPrice,
  UnitPriceMayBeBasedOnLengthOfProcedureOrService: v2_0205_UnitPriceMayBeBasedOnLengthOfProcedureOrService,
};
const v2_0206_AddInsert: Coding = {
  code: 'A',
  display: 'Add/Insert',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0206',
};
const v2_0206_Delete: Coding = {
  code: 'D',
  display: 'Delete',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0206',
};
const v2_0206_UsedInSnapshotMode: Coding = {
  code: 'S',
  display: 'Used in Snapshot mode',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0206',
};
const v2_0206_Update: Coding = {
  code: 'U',
  display: 'Update',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0206',
};
const v2_0206_NoChange: Coding = {
  code: 'X',
  display: 'No Change',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0206',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0206 ( Segment Action Code)
 */
export const V20206 = {
  AddInsert: v2_0206_AddInsert,
  Delete: v2_0206_Delete,
  UsedInSnapshotMode: v2_0206_UsedInSnapshotMode,
  Update: v2_0206_Update,
  NoChange: v2_0206_NoChange,
};
const v2_0207_Archive: Coding = {
  code: 'A',
  display: 'Archive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0207',
};
const v2_0207_InitialLoad: Coding = {
  code: 'I',
  display: 'Initial load',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0207',
};
const v2_0207_NotPresentTheDefaultMeaningCurrentProcessing: Coding = {
  code: 'Not present',
  display: 'Not present (the default, meaning current  processing)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0207',
};
const v2_0207_RestoreFromArchive: Coding = {
  code: 'R',
  display: 'Restore from archive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0207',
};
const v2_0207_CurrentProcessingTransmittedAtIntervalsScheduledOrOnDemand: Coding = {
  code: 'T',
  display: 'Current processing, transmitted at intervals (scheduled or on demand)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0207',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0207 ( Processing Mode)
 */
export const V20207 = {
  Archive: v2_0207_Archive,
  InitialLoad: v2_0207_InitialLoad,
  NotPresentTheDefaultMeaningCurrentProcessing: v2_0207_NotPresentTheDefaultMeaningCurrentProcessing,
  RestoreFromArchive: v2_0207_RestoreFromArchive,
  CurrentProcessingTransmittedAtIntervalsScheduledOrOnDemand: v2_0207_CurrentProcessingTransmittedAtIntervalsScheduledOrOnDemand,
};
const v2_0208_ApplicationError: Coding = {
  code: 'AE',
  display: 'Application error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0208',
};
const v2_0208_ApplicationReject: Coding = {
  code: 'AR',
  display: 'Application reject',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0208',
};
const v2_0208_NoDataFoundNoErrors: Coding = {
  code: 'NF',
  display: 'No data found, no errors',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0208',
};
const v2_0208_DataFoundNoErrorsThisIsTheDefault: Coding = {
  code: 'OK',
  display: 'Data found, no errors (this is the default)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0208',
};
const v2_0208_ProtectedData: Coding = {
  code: 'PD',
  display: 'Protected data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0208',
};
const v2_0208_TooMuchDataFound: Coding = {
  code: 'TM',
  display: 'Too much data found',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0208',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0208 ( Query Response Status)
 */
export const V20208 = {
  ApplicationError: v2_0208_ApplicationError,
  ApplicationReject: v2_0208_ApplicationReject,
  NoDataFoundNoErrors: v2_0208_NoDataFoundNoErrors,
  DataFoundNoErrorsThisIsTheDefault: v2_0208_DataFoundNoErrorsThisIsTheDefault,
  ProtectedData: v2_0208_ProtectedData,
  TooMuchDataFound: v2_0208_TooMuchDataFound,
};
const v2_0209_Contains: Coding = {
  code: 'CT',
  display: 'Contains',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0209',
};
const v2_0209_Equal: Coding = {
  code: 'EQ',
  display: 'Equal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0209',
};
const v2_0209_GreaterThanOrEqual: Coding = {
  code: 'GE',
  display: 'Greater than or equal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0209',
};
const v2_0209_Generic: Coding = {
  code: 'GN',
  display: 'Generic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0209',
};
const v2_0209_GreaterThan: Coding = {
  code: 'GT',
  display: 'Greater than',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0209',
};
const v2_0209_LessThanOrEqual: Coding = {
  code: 'LE',
  display: 'Less than or equal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0209',
};
const v2_0209_LessThan: Coding = {
  code: 'LT',
  display: 'Less than',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0209',
};
const v2_0209_NotEqual: Coding = {
  code: 'NE',
  display: 'Not Equal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0209',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0209 ( Relational Operator)
 */
export const V20209 = {
  Contains: v2_0209_Contains,
  Equal: v2_0209_Equal,
  GreaterThanOrEqual: v2_0209_GreaterThanOrEqual,
  Generic: v2_0209_Generic,
  GreaterThan: v2_0209_GreaterThan,
  LessThanOrEqual: v2_0209_LessThanOrEqual,
  LessThan: v2_0209_LessThan,
  NotEqual: v2_0209_NotEqual,
};
const v2_0210_Default: Coding = {
  code: 'AND',
  display: 'Default',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0210',
};
const v2_0210_OR: Coding = {
  code: 'OR',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0210',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0210 ( Relational Conjunction)
 */
export const V20210 = {
  Default: v2_0210_Default,
  OR: v2_0210_OR,
};
const v2_0211_ThePrintableCharactersFromTheISO88591CharacterSet: Coding = {
  code: '8859/1',
  display: 'The printable characters from the ISO 8859/1 Character set',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_ThePrintableCharactersFromTheISO885915Latin15: Coding = {
  code: '8859/15',
  display: 'The printable characters from the ISO 8859/15 (Latin-15)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_ThePrintableCharactersFromTheISO88592CharacterSet: Coding = {
  code: '8859/2',
  display: 'The printable characters from the ISO 8859/2 Character set',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_ThePrintableCharactersFromTheISO88593CharacterSet: Coding = {
  code: '8859/3',
  display: 'The printable characters from the ISO 8859/3 Character set',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_ThePrintableCharactersFromTheISO88594CharacterSet: Coding = {
  code: '8859/4',
  display: 'The printable characters from the ISO 8859/4 Character set',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_ThePrintableCharactersFromTheISO88595CharacterSet: Coding = {
  code: '8859/5',
  display: 'The printable characters from the ISO 8859/5 Character set',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_ThePrintableCharactersFromTheISO88596CharacterSet: Coding = {
  code: '8859/6',
  display: 'The printable characters from the ISO 8859/6 Character set',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_ThePrintableCharactersFromTheISO88597CharacterSet: Coding = {
  code: '8859/7',
  display: 'The printable characters from the ISO 8859/7 Character set',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_ThePrintableCharactersFromTheISO88598CharacterSet: Coding = {
  code: '8859/8',
  display: 'The printable characters from the ISO 8859/8 Character set',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_ThePrintableCharactersFromTheISO88599CharacterSet: Coding = {
  code: '8859/9',
  display: 'The printable characters from the ISO 8859/9 Character set',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_ThePrintable7BitASCIICharacterSet: Coding = {
  code: 'ASCII',
  display: 'The printable 7-bit ASCII character set.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_CodeForTaiwaneseCharacterSetBIG5: Coding = {
  code: 'BIG-5',
  display: 'Code for Taiwanese Character Set (BIG-5)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_CodeForTaiwaneseCharacterSetCNS116431992: Coding = {
  code: 'CNS 11643-1992',
  display: 'Code for Taiwanese Character Set (CNS 11643-1992)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_CodeForChineseCharacterSetGB180302000: Coding = {
  code: 'GB 18030-2000',
  display: 'Code for Chinese Character Set (GB 18030-2000)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_CodeForInformationExchangeOneByteJISX02011976: Coding = {
  code: 'ISO IR14',
  display: 'Code for Information Exchange (one byte)(JIS X 0201-1976).',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_CodeOfTheSupplementaryJapaneseGraphicCharacterSetForInformationInterchangeJISX02121990: Coding = {
  code: 'ISO IR159',
  display: 'Code of the supplementary Japanese Graphic Character set for information interchange (JIS X 0212-1990).',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_ASCIIGraphicCharacterSetConsistingOf94Characters: Coding = {
  code: 'ISO IR6',
  display: 'ASCII graphic character set consisting of 94 characters.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_CodeForTheJapaneseGraphicCharacterSetForInformationInterchangeJISX02081990: Coding = {
  code: 'ISO IR87',
  display: 'Code for the Japanese Graphic Character set for information interchange (JIS X 0208-1990),',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_CodeForKoreanCharacterSetKSX1001: Coding = {
  code: 'KS X 1001',
  display: 'Code for Korean Character Set (KS X 1001)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_TheWorldWideCharacterStandardFromISOIEC1064611993: Coding = {
  code: 'UNICODE',
  display: 'The world wide character standard from ISO/IEC 10646-1-1993',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
const v2_0211_UCSTransformationFormat8BitForm: Coding = {
  code: 'UNICODE UTF-8',
  display: 'UCS Transformation Format, 8-bit form',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0211',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0211 ( Alternate Character Sets)
 */
export const V20211 = {
  ThePrintableCharactersFromTheISO88591CharacterSet: v2_0211_ThePrintableCharactersFromTheISO88591CharacterSet,
  ThePrintableCharactersFromTheISO885915Latin15: v2_0211_ThePrintableCharactersFromTheISO885915Latin15,
  ThePrintableCharactersFromTheISO88592CharacterSet: v2_0211_ThePrintableCharactersFromTheISO88592CharacterSet,
  ThePrintableCharactersFromTheISO88593CharacterSet: v2_0211_ThePrintableCharactersFromTheISO88593CharacterSet,
  ThePrintableCharactersFromTheISO88594CharacterSet: v2_0211_ThePrintableCharactersFromTheISO88594CharacterSet,
  ThePrintableCharactersFromTheISO88595CharacterSet: v2_0211_ThePrintableCharactersFromTheISO88595CharacterSet,
  ThePrintableCharactersFromTheISO88596CharacterSet: v2_0211_ThePrintableCharactersFromTheISO88596CharacterSet,
  ThePrintableCharactersFromTheISO88597CharacterSet: v2_0211_ThePrintableCharactersFromTheISO88597CharacterSet,
  ThePrintableCharactersFromTheISO88598CharacterSet: v2_0211_ThePrintableCharactersFromTheISO88598CharacterSet,
  ThePrintableCharactersFromTheISO88599CharacterSet: v2_0211_ThePrintableCharactersFromTheISO88599CharacterSet,
  ThePrintable7BitASCIICharacterSet: v2_0211_ThePrintable7BitASCIICharacterSet,
  CodeForTaiwaneseCharacterSetBIG5: v2_0211_CodeForTaiwaneseCharacterSetBIG5,
  CodeForTaiwaneseCharacterSetCNS116431992: v2_0211_CodeForTaiwaneseCharacterSetCNS116431992,
  CodeForChineseCharacterSetGB180302000: v2_0211_CodeForChineseCharacterSetGB180302000,
  CodeForInformationExchangeOneByteJISX02011976: v2_0211_CodeForInformationExchangeOneByteJISX02011976,
  CodeOfTheSupplementaryJapaneseGraphicCharacterSetForInformationInterchangeJISX02121990: v2_0211_CodeOfTheSupplementaryJapaneseGraphicCharacterSetForInformationInterchangeJISX02121990,
  ASCIIGraphicCharacterSetConsistingOf94Characters: v2_0211_ASCIIGraphicCharacterSetConsistingOf94Characters,
  CodeForTheJapaneseGraphicCharacterSetForInformationInterchangeJISX02081990: v2_0211_CodeForTheJapaneseGraphicCharacterSetForInformationInterchangeJISX02081990,
  CodeForKoreanCharacterSetKSX1001: v2_0211_CodeForKoreanCharacterSetKSX1001,
  TheWorldWideCharacterStandardFromISOIEC1064611993: v2_0211_TheWorldWideCharacterStandardFromISOIEC1064611993,
  UCSTransformationFormat8BitForm: v2_0211_UCSTransformationFormat8BitForm,
};
const v2_0213_TheVisitIsMarkedForDeletionAndTheUserCannotEnterNewDataAgainstIt: Coding = {
  code: 'D',
  display: 'The visit is marked for deletion and the user cannot enter new data against it.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0213',
};
const v2_0213_TheVisitIsMarkedInactiveAndTheUserCannotEnterNewDataAgainstIt: Coding = {
  code: 'I',
  display: 'The visit is marked inactive and the user cannot enter new data against it.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0213',
};
const v2_0213_MarkedForPurgeUserIsNoLongerAbleToUpdateTheVisit: Coding = {
  code: 'P',
  display: 'Marked for purge.  User is no longer able to update the visit.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0213',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0213 ( Purge Status)
 */
export const V20213 = {
  TheVisitIsMarkedForDeletionAndTheUserCannotEnterNewDataAgainstIt: v2_0213_TheVisitIsMarkedForDeletionAndTheUserCannotEnterNewDataAgainstIt,
  TheVisitIsMarkedInactiveAndTheUserCannotEnterNewDataAgainstIt: v2_0213_TheVisitIsMarkedInactiveAndTheUserCannotEnterNewDataAgainstIt,
  MarkedForPurgeUserIsNoLongerAbleToUpdateTheVisit: v2_0213_MarkedForPurgeUserIsNoLongerAbleToUpdateTheVisit,
};
const v2_0214_ChildHealthAssistance: Coding = {
  code: 'CH',
  display: 'Child Health Assistance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0214',
};
const v2_0214_ElectiveSurgeryProgram: Coding = {
  code: 'ES',
  display: 'Elective Surgery Program',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0214',
};
const v2_0214_FamilyPlanning: Coding = {
  code: 'FP',
  display: 'Family Planning',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0214',
};
const v2_0214_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0214',
};
const v2_0214_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0214',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0214 ( Special Program Code)
 */
export const V20214 = {
  ChildHealthAssistance: v2_0214_ChildHealthAssistance,
  ElectiveSurgeryProgram: v2_0214_ElectiveSurgeryProgram,
  FamilyPlanning: v2_0214_FamilyPlanning,
  Other: v2_0214_Other,
  Unknown: v2_0214_Unknown,
};
const v2_0215_FamilyOnly: Coding = {
  code: 'F',
  display: 'Family only',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0215',
};
const v2_0215_NoPublicity: Coding = {
  code: 'N',
  display: 'No Publicity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0215',
};
const v2_0215_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0215',
};
const v2_0215_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0215',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0215 ( Publicity Code)
 */
export const V20215 = {
  FamilyOnly: v2_0215_FamilyOnly,
  NoPublicity: v2_0215_NoPublicity,
  Other: v2_0215_Other,
  Unknown: v2_0215_Unknown,
};
const v2_0216_ActiveInpatient: Coding = {
  code: 'AI',
  display: 'Active Inpatient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0216',
};
const v2_0216_DischargedInpatient: Coding = {
  code: 'DI',
  display: 'Discharged Inpatient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0216',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0216 ( Patient Status Code)
 */
export const V20216 = {
  ActiveInpatient: v2_0216_ActiveInpatient,
  DischargedInpatient: v2_0216_DischargedInpatient,
};
const v2_0217_Emergency: Coding = {
  code: '1',
  display: 'Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0217',
};
const v2_0217_Urgent: Coding = {
  code: '2',
  display: 'Urgent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0217',
};
const v2_0217_Elective: Coding = {
  code: '3',
  display: 'Elective',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0217',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0217 ( Visit priority code)
 */
export const V20217 = {
  Emergency: v2_0217_Emergency,
  Urgent: v2_0217_Urgent,
  Elective: v2_0217_Elective,
};
const v2_0220_Alone: Coding = {
  code: 'A',
  display: 'Alone',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0220',
};
const v2_0220_Family: Coding = {
  code: 'F',
  display: 'Family',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0220',
};
const v2_0220_Institution: Coding = {
  code: 'I',
  display: 'Institution',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0220',
};
const v2_0220_Relative: Coding = {
  code: 'R',
  display: 'Relative',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0220',
};
const v2_0220_SpouseOnly: Coding = {
  code: 'S',
  display: 'Spouse Only',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0220',
};
const v2_0220_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0220',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0220 ( Living Arrangements)
 */
export const V20220 = {
  Alone: v2_0220_Alone,
  Family: v2_0220_Family,
  Institution: v2_0220_Institution,
  Relative: v2_0220_Relative,
  SpouseOnly: v2_0220_SpouseOnly,
  Unknown: v2_0220_Unknown,
};
const v2_0223_SmallChildrenDependent: Coding = {
  code: 'C',
  display: 'Small Children Dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0223',
};
const v2_0223_MedicalSupervisionRequired: Coding = {
  code: 'M',
  display: 'Medical Supervision Required',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0223',
};
const v2_0223_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0223',
};
const v2_0223_SpouseDependent: Coding = {
  code: 'S',
  display: 'Spouse Dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0223',
};
const v2_0223_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0223',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0223 ( Living Dependency)
 */
export const V20223 = {
  SmallChildrenDependent: v2_0223_SmallChildrenDependent,
  MedicalSupervisionRequired: v2_0223_MedicalSupervisionRequired,
  Other: v2_0223_Other,
  SpouseDependent: v2_0223_SpouseDependent,
  Unknown: v2_0223_Unknown,
};
const v2_0224_Arranged: Coding = {
  code: 'A',
  display: 'Arranged',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0224',
};
const v2_0224_NotArranged: Coding = {
  code: 'N',
  display: 'Not Arranged',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0224',
};
const v2_0224_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0224',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0224 ( Transport Arranged)
 */
export const V20224 = {
  Arranged: v2_0224_Arranged,
  NotArranged: v2_0224_NotArranged,
  Unknown: v2_0224_Unknown,
};
const v2_0225_NotRequired: Coding = {
  code: 'N',
  display: 'Not Required',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0225',
};
const v2_0225_Required: Coding = {
  code: 'R',
  display: 'Required',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0225',
};
const v2_0225_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0225',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0225 ( Escort Required)
 */
export const V20225 = {
  NotRequired: v2_0225_NotRequired,
  Required: v2_0225_Required,
  Unknown: v2_0225_Unknown,
};
const v2_0228_Consultation: Coding = {
  code: 'C',
  display: 'Consultation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0228',
};
const v2_0228_Diagnosis: Coding = {
  code: 'D',
  display: 'Diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0228',
};
const v2_0228_InvasiveProcedureNotClassifiedElsewhereIVCatheterEtc: Coding = {
  code: 'I',
  display: 'Invasive procedure not classified elsewhere (I.V., catheter, etc.)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0228',
};
const v2_0228_MedicationAntibiotic: Coding = {
  code: 'M',
  display: 'Medication (antibiotic)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0228',
};
const v2_0228_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0228',
};
const v2_0228_RadiologicalSchedulingNotUsingICDACodes: Coding = {
  code: 'R',
  display: 'Radiological scheduling (not using ICDA codes)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0228',
};
const v2_0228_SignAndSymptom: Coding = {
  code: 'S',
  display: 'Sign and symptom',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0228',
};
const v2_0228_TissueDiagnosis: Coding = {
  code: 'T',
  display: 'Tissue diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0228',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0228 ( Diagnosis Classification)
 */
export const V20228 = {
  Consultation: v2_0228_Consultation,
  Diagnosis: v2_0228_Diagnosis,
  InvasiveProcedureNotClassifiedElsewhereIVCatheterEtc: v2_0228_InvasiveProcedureNotClassifiedElsewhereIVCatheterEtc,
  MedicationAntibiotic: v2_0228_MedicationAntibiotic,
  Other: v2_0228_Other,
  RadiologicalSchedulingNotUsingICDACodes: v2_0228_RadiologicalSchedulingNotUsingICDACodes,
  SignAndSymptom: v2_0228_SignAndSymptom,
  TissueDiagnosis: v2_0228_TissueDiagnosis,
};
const v2_0229_NoSuggestedValuesDefined: Coding = {
  code: '...',
  display: 'No suggested values defined',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0229',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0229 ( Outlier Type)
 */
export const V20229 = {
  NoSuggestedValuesDefined: v2_0229_NoSuggestedValuesDefined,
};
const v2_0230_Anesthesia: Coding = {
  code: 'A',
  display: 'Anesthesia',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0230',
};
const v2_0230_DiagnosticProcedure: Coding = {
  code: 'D',
  display: 'Diagnostic procedure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0230',
};
const v2_0230_InvasiveProcedureNotClassifiedElsewhereEGIVCatheterEtc: Coding = {
  code: 'I',
  display: 'Invasive procedure not classified elsewhere (e.g., IV, catheter, etc.)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0230',
};
const v2_0230_ProcedureForTreatmentTherapeuticIncludingOperations: Coding = {
  code: 'P',
  display: 'Procedure for treatment (therapeutic, including operations)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0230',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0230 ( Procedure Functional Type)
 */
export const V20230 = {
  Anesthesia: v2_0230_Anesthesia,
  DiagnosticProcedure: v2_0230_DiagnosticProcedure,
  InvasiveProcedureNotClassifiedElsewhereEGIVCatheterEtc: v2_0230_InvasiveProcedureNotClassifiedElsewhereEGIVCatheterEtc,
  ProcedureForTreatmentTherapeuticIncludingOperations: v2_0230_ProcedureForTreatmentTherapeuticIncludingOperations,
};
const v2_0231_FullTimeStudent: Coding = {
  code: 'F',
  display: 'Full-time student',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0231',
};
const v2_0231_NotAStudent: Coding = {
  code: 'N',
  display: 'Not a student',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0231',
};
const v2_0231_PartTimeStudent: Coding = {
  code: 'P',
  display: 'Part-time student',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0231',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0231 ( Student Status)
 */
export const V20231 = {
  FullTimeStudent: v2_0231_FullTimeStudent,
  NotAStudent: v2_0231_NotAStudent,
  PartTimeStudent: v2_0231_PartTimeStudent,
};
const v2_0232_MedicareClaimStatus: Coding = {
  code: '01',
  display: 'Medicare claim status',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0232',
};
const v2_0232_MedicaidClaimStatus: Coding = {
  code: '02',
  display: 'Medicaid claim status',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0232',
};
const v2_0232_NameAddressChange: Coding = {
  code: '03',
  display: 'Name/address change',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0232',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0232 ( Insurance Company Contact Reason)
 */
export const V20232 = {
  MedicareClaimStatus: v2_0232_MedicareClaimStatus,
  MedicaidClaimStatus: v2_0232_MedicaidClaimStatus,
  NameAddressChange: v2_0232_NameAddressChange,
};
const v2_0234_VAL10DayReport: Coding = {
  code: '10D',
  display: '10 day report',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0234',
};
const v2_0234_VAL15DayReport: Coding = {
  code: '15D',
  display: '15 day report',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0234',
};
const v2_0234_VAL30DayReport: Coding = {
  code: '30D',
  display: '30 day report',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0234',
};
const v2_0234_VAL3DayReport: Coding = {
  code: '3D',
  display: '3 day report',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0234',
};
const v2_0234_VAL7DayReport: Coding = {
  code: '7D',
  display: '7 day report',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0234',
};
const v2_0234_AdditionalInformation: Coding = {
  code: 'AD',
  display: 'Additional information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0234',
};
const v2_0234_Correction: Coding = {
  code: 'CO',
  display: 'Correction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0234',
};
const v2_0234_DeviceEvaluation: Coding = {
  code: 'DE',
  display: 'Device evaluation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0234',
};
const v2_0234_Periodic: Coding = {
  code: 'PD',
  display: 'Periodic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0234',
};
const v2_0234_RequestedInformation: Coding = {
  code: 'RQ',
  display: 'Requested information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0234',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0234 ( Report Timing)
 */
export const V20234 = {
  VAL10DayReport: v2_0234_VAL10DayReport,
  VAL15DayReport: v2_0234_VAL15DayReport,
  VAL30DayReport: v2_0234_VAL30DayReport,
  VAL3DayReport: v2_0234_VAL3DayReport,
  VAL7DayReport: v2_0234_VAL7DayReport,
  AdditionalInformation: v2_0234_AdditionalInformation,
  Correction: v2_0234_Correction,
  DeviceEvaluation: v2_0234_DeviceEvaluation,
  Periodic: v2_0234_Periodic,
  RequestedInformation: v2_0234_RequestedInformation,
};
const v2_0235_ClinicalTrial: Coding = {
  code: 'C',
  display: 'Clinical trial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0235',
};
const v2_0235_DatabaseRegistryPoisonControlCenter: Coding = {
  code: 'D',
  display: 'Database/registry/poison control center',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0235',
};
const v2_0235_Distributor: Coding = {
  code: 'E',
  display: 'Distributor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0235',
};
const v2_0235_HealthProfessional: Coding = {
  code: 'H',
  display: 'Health professional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0235',
};
const v2_0235_Literature: Coding = {
  code: 'L',
  display: 'Literature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0235',
};
const v2_0235_ManufacturerMarketingAuthorityHolder: Coding = {
  code: 'M',
  display: 'Manufacturer/marketing authority holder',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0235',
};
const v2_0235_NonHealthcareProfessional: Coding = {
  code: 'N',
  display: 'Non-healthcare professional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0235',
};
const v2_0235_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0235',
};
const v2_0235_Patient: Coding = {
  code: 'P',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0235',
};
const v2_0235_RegulatoryAgency: Coding = {
  code: 'R',
  display: 'Regulatory agency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0235',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0235 ( Report Source)
 */
export const V20235 = {
  ClinicalTrial: v2_0235_ClinicalTrial,
  DatabaseRegistryPoisonControlCenter: v2_0235_DatabaseRegistryPoisonControlCenter,
  Distributor: v2_0235_Distributor,
  HealthProfessional: v2_0235_HealthProfessional,
  Literature: v2_0235_Literature,
  ManufacturerMarketingAuthorityHolder: v2_0235_ManufacturerMarketingAuthorityHolder,
  NonHealthcareProfessional: v2_0235_NonHealthcareProfessional,
  Other: v2_0235_Other,
  Patient: v2_0235_Patient,
  RegulatoryAgency: v2_0235_RegulatoryAgency,
};
const v2_0236_Distributor: Coding = {
  code: 'D',
  display: 'Distributor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0236',
};
const v2_0236_LocalFacilityUserFacility: Coding = {
  code: 'L',
  display: 'Local facility/user facility',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0236',
};
const v2_0236_Manufacturer: Coding = {
  code: 'M',
  display: 'Manufacturer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0236',
};
const v2_0236_RegulatoryAgency: Coding = {
  code: 'R',
  display: 'Regulatory agency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0236',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0236 ( Reported To)
 */
export const V20236 = {
  Distributor: v2_0236_Distributor,
  LocalFacilityUserFacility: v2_0236_LocalFacilityUserFacility,
  Manufacturer: v2_0236_Manufacturer,
  RegulatoryAgency: v2_0236_RegulatoryAgency,
};
const v2_0237_Abuse: Coding = {
  code: 'A',
  display: 'Abuse',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0237',
};
const v2_0237_UnexpectedBeneficialEffect: Coding = {
  code: 'B',
  display: 'Unexpected beneficial effect',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0237',
};
const v2_0237_Dependency: Coding = {
  code: 'D',
  display: 'Dependency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0237',
};
const v2_0237_Interaction: Coding = {
  code: 'I',
  display: 'Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0237',
};
const v2_0237_LackOfExpectTherapeuticEffect: Coding = {
  code: 'L',
  display: 'Lack of expect therapeutic effect',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0237',
};
const v2_0237_Misuse: Coding = {
  code: 'M',
  display: 'Misuse',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0237',
};
const v2_0237_Overdose: Coding = {
  code: 'O',
  display: 'Overdose',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0237',
};
const v2_0237_DrugWithdrawal: Coding = {
  code: 'W',
  display: 'Drug withdrawal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0237',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0237 ( Event Qualification)
 */
export const V20237 = {
  Abuse: v2_0237_Abuse,
  UnexpectedBeneficialEffect: v2_0237_UnexpectedBeneficialEffect,
  Dependency: v2_0237_Dependency,
  Interaction: v2_0237_Interaction,
  LackOfExpectTherapeuticEffect: v2_0237_LackOfExpectTherapeuticEffect,
  Misuse: v2_0237_Misuse,
  Overdose: v2_0237_Overdose,
  DrugWithdrawal: v2_0237_DrugWithdrawal,
};
const v2_0238_No: Coding = {
  code: 'N',
  display: 'No',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0238',
};
const v2_0238_Significant: Coding = {
  code: 'S',
  display: 'Significant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0238',
};
const v2_0238_Yes: Coding = {
  code: 'Y',
  display: 'Yes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0238',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0238 ( Event Seriousness)
 */
export const V20238 = {
  No: v2_0238_No,
  Significant: v2_0238_Significant,
  Yes: v2_0238_Yes,
};
const v2_0239_No: Coding = {
  code: 'N',
  display: 'No',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0239',
};
const v2_0239_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0239',
};
const v2_0239_Yes: Coding = {
  code: 'Y',
  display: 'Yes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0239',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0239 ( Event Expected)
 */
export const V20239 = {
  No: v2_0239_No,
  Unknown: v2_0239_Unknown,
  Yes: v2_0239_Yes,
};
const v2_0240_CongenitalAnomalyBirthDefect: Coding = {
  code: 'C',
  display: 'Congenital anomaly/birth defect',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0240',
};
const v2_0240_Death: Coding = {
  code: 'D',
  display: 'Death',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0240',
};
const v2_0240_CausedHospitalized: Coding = {
  code: 'H',
  display: 'Caused hospitalized',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0240',
};
const v2_0240_IncapacityWhichIsSignificantPersistentOrPermanent: Coding = {
  code: 'I',
  display: 'Incapacity which is significant, persistent or permanent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0240',
};
const v2_0240_DisabilityWhichIsSignificantPersistentOrPermanent: Coding = {
  code: 'J',
  display: 'Disability which is significant, persistent or permanent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0240',
};
const v2_0240_LifeThreatening: Coding = {
  code: 'L',
  display: 'Life threatening',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0240',
};
const v2_0240_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0240',
};
const v2_0240_ProlongedHospitalization: Coding = {
  code: 'P',
  display: 'Prolonged hospitalization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0240',
};
const v2_0240_RequiredInterventionToPreventPermanentImpairmentDamage: Coding = {
  code: 'R',
  display: 'Required intervention to prevent permanent impairment/damage',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0240',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0240 ( Event Consequence)
 */
export const V20240 = {
  CongenitalAnomalyBirthDefect: v2_0240_CongenitalAnomalyBirthDefect,
  Death: v2_0240_Death,
  CausedHospitalized: v2_0240_CausedHospitalized,
  IncapacityWhichIsSignificantPersistentOrPermanent: v2_0240_IncapacityWhichIsSignificantPersistentOrPermanent,
  DisabilityWhichIsSignificantPersistentOrPermanent: v2_0240_DisabilityWhichIsSignificantPersistentOrPermanent,
  LifeThreatening: v2_0240_LifeThreatening,
  Other: v2_0240_Other,
  ProlongedHospitalization: v2_0240_ProlongedHospitalization,
  RequiredInterventionToPreventPermanentImpairmentDamage: v2_0240_RequiredInterventionToPreventPermanentImpairmentDamage,
};
const v2_0241_Died: Coding = {
  code: 'D',
  display: 'Died',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0241',
};
const v2_0241_FullyRecovered: Coding = {
  code: 'F',
  display: 'Fully recovered',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0241',
};
const v2_0241_NotRecoveringUnchanged: Coding = {
  code: 'N',
  display: 'Not recovering/unchanged',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0241',
};
const v2_0241_Recovering: Coding = {
  code: 'R',
  display: 'Recovering',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0241',
};
const v2_0241_Sequelae: Coding = {
  code: 'S',
  display: 'Sequelae',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0241',
};
const v2_0241_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0241',
};
const v2_0241_Worsening: Coding = {
  code: 'W',
  display: 'Worsening',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0241',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0241 ( Patient Outcome)
 */
export const V20241 = {
  Died: v2_0241_Died,
  FullyRecovered: v2_0241_FullyRecovered,
  NotRecoveringUnchanged: v2_0241_NotRecoveringUnchanged,
  Recovering: v2_0241_Recovering,
  Sequelae: v2_0241_Sequelae,
  Unknown: v2_0241_Unknown,
  Worsening: v2_0241_Worsening,
};
const v2_0242_HealthCareConsumerPatient: Coding = {
  code: 'C',
  display: 'Health care consumer/patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0242',
};
const v2_0242_OtherHealthProfessional: Coding = {
  code: 'H',
  display: 'Other health professional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0242',
};
const v2_0242_LawyerAttorney: Coding = {
  code: 'L',
  display: 'Lawyer/attorney',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0242',
};
const v2_0242_MidLevelProfessionalNurseNursePractitionerPhysicianQuoteSAssistant: Coding = {
  code: 'M',
  display: "Mid-level professional (nurse, nurse practitioner, physician's assistant)",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0242',
};
const v2_0242_OtherNonHealthProfessional: Coding = {
  code: 'O',
  display: 'Other non-health professional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0242',
};
const v2_0242_PhysicianOsteopathHomeopath: Coding = {
  code: 'P',
  display: 'Physician (osteopath, homeopath)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0242',
};
const v2_0242_Pharmacist: Coding = {
  code: 'R',
  display: 'Pharmacist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0242',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0242 ( Primary Observer's Qualification)
 */
export const V20242 = {
  HealthCareConsumerPatient: v2_0242_HealthCareConsumerPatient,
  OtherHealthProfessional: v2_0242_OtherHealthProfessional,
  LawyerAttorney: v2_0242_LawyerAttorney,
  MidLevelProfessionalNurseNursePractitionerPhysicianQuoteSAssistant: v2_0242_MidLevelProfessionalNurseNursePractitionerPhysicianQuoteSAssistant,
  OtherNonHealthProfessional: v2_0242_OtherNonHealthProfessional,
  PhysicianOsteopathHomeopath: v2_0242_PhysicianOsteopathHomeopath,
  Pharmacist: v2_0242_Pharmacist,
};
const v2_0243_No: Coding = {
  code: 'N',
  display: 'No',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0243',
};
const v2_0243_NotApplicable: Coding = {
  code: 'NA',
  display: 'Not applicable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0243',
};
const v2_0243_Yes: Coding = {
  code: 'Y',
  display: 'Yes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0243',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0243 ( Identity may be Divulged)
 */
export const V20243 = {
  No: v2_0243_No,
  NotApplicable: v2_0243_NotApplicable,
  Yes: v2_0243_Yes,
};
const v2_0247_EvaluationAnticipatedButNotYetBegun: Coding = {
  code: 'A',
  display: 'Evaluation anticipated, but not yet begun',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0247',
};
const v2_0247_ProductReceivedInConditionWhichMadeAnalysisImpossible: Coding = {
  code: 'C',
  display: 'Product received in condition which made analysis impossible',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0247',
};
const v2_0247_ProductDiscardedUnableToFollowUp: Coding = {
  code: 'D',
  display: 'Product discarded -- unable to follow up',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0247',
};
const v2_0247_ProductRemainsImplantedUnableToFollowUp: Coding = {
  code: 'I',
  display: 'Product remains implanted -- unable to follow up',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0247',
};
const v2_0247_ProblemAlreadyKnownNoEvaluationNecessary: Coding = {
  code: 'K',
  display: 'Problem already known, no evaluation necessary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0247',
};
const v2_0247_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0247',
};
const v2_0247_EvaluationInProgress: Coding = {
  code: 'P',
  display: 'Evaluation in progress',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0247',
};
const v2_0247_ProductUnderQuarantineUnableToFollowUp: Coding = {
  code: 'Q',
  display: 'Product under quarantine -- unable to follow up',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0247',
};
const v2_0247_ProductUnderRecallCorrectiveAction: Coding = {
  code: 'R',
  display: 'Product under recall/corrective action',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0247',
};
const v2_0247_ProductUnavailableForFollowUpInvestigation: Coding = {
  code: 'U',
  display: 'Product unavailable for follow up investigation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0247',
};
const v2_0247_ProductNotMadeByCompany: Coding = {
  code: 'X',
  display: 'Product not made by company',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0247',
};
const v2_0247_EvaluationCompleted: Coding = {
  code: 'Y',
  display: 'Evaluation completed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0247',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0247 ( Start of Evaluation)
 */
export const V20247 = {
  EvaluationAnticipatedButNotYetBegun: v2_0247_EvaluationAnticipatedButNotYetBegun,
  ProductReceivedInConditionWhichMadeAnalysisImpossible: v2_0247_ProductReceivedInConditionWhichMadeAnalysisImpossible,
  ProductDiscardedUnableToFollowUp: v2_0247_ProductDiscardedUnableToFollowUp,
  ProductRemainsImplantedUnableToFollowUp: v2_0247_ProductRemainsImplantedUnableToFollowUp,
  ProblemAlreadyKnownNoEvaluationNecessary: v2_0247_ProblemAlreadyKnownNoEvaluationNecessary,
  Other: v2_0247_Other,
  EvaluationInProgress: v2_0247_EvaluationInProgress,
  ProductUnderQuarantineUnableToFollowUp: v2_0247_ProductUnderQuarantineUnableToFollowUp,
  ProductUnderRecallCorrectiveAction: v2_0247_ProductUnderRecallCorrectiveAction,
  ProductUnavailableForFollowUpInvestigation: v2_0247_ProductUnavailableForFollowUpInvestigation,
  ProductNotMadeByCompany: v2_0247_ProductNotMadeByCompany,
  EvaluationCompleted: v2_0247_EvaluationCompleted,
};
const v2_0248_ActualProductInvolvedInIncidentWasEvaluated: Coding = {
  code: 'A',
  display: 'Actual product involved in incident was evaluated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0248',
};
const v2_0248_AProductFromTheSameLotAsTheActualProductInvolvedWasEvaluated: Coding = {
  code: 'L',
  display: 'A product from the same lot as the actual product involved was evaluated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0248',
};
const v2_0248_AProductFromAControlledNonRelatedInventoryWasEvaluated: Coding = {
  code: 'N',
  display: 'A product from a controlled/non-related inventory was evaluated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0248',
};
const v2_0248_AProductFromAReserveSampleWasEvaluated: Coding = {
  code: 'R',
  display: 'A product from a reserve sample was evaluated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0248',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0248 ( Product Source)
 */
export const V20248 = {
  ActualProductInvolvedInIncidentWasEvaluated: v2_0248_ActualProductInvolvedInIncidentWasEvaluated,
  AProductFromTheSameLotAsTheActualProductInvolvedWasEvaluated: v2_0248_AProductFromTheSameLotAsTheActualProductInvolvedWasEvaluated,
  AProductFromAControlledNonRelatedInventoryWasEvaluated: v2_0248_AProductFromAControlledNonRelatedInventoryWasEvaluated,
  AProductFromAReserveSampleWasEvaluated: v2_0248_AProductFromAReserveSampleWasEvaluated,
};
const v2_0250_HighlyProbable: Coding = {
  code: 'H',
  display: 'Highly probable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0250',
};
const v2_0250_Improbable: Coding = {
  code: 'I',
  display: 'Improbable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0250',
};
const v2_0250_ModeratelyProbable: Coding = {
  code: 'M',
  display: 'Moderately probable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0250',
};
const v2_0250_NotRelated: Coding = {
  code: 'N',
  display: 'Not related',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0250',
};
const v2_0250_SomewhatProbable: Coding = {
  code: 'S',
  display: 'Somewhat probable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0250',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0250 ( Relatedness Assessment)
 */
export const V20250 = {
  HighlyProbable: v2_0250_HighlyProbable,
  Improbable: v2_0250_Improbable,
  ModeratelyProbable: v2_0250_ModeratelyProbable,
  NotRelated: v2_0250_NotRelated,
  SomewhatProbable: v2_0250_SomewhatProbable,
};
const v2_0251_ProductDoseOrFrequencyOfUseIncreased: Coding = {
  code: 'DI',
  display: 'Product dose or frequency of use increased',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0251',
};
const v2_0251_ProductDoseOrFrequencyOfUseReduced: Coding = {
  code: 'DR',
  display: 'Product dose or frequency of use reduced',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0251',
};
const v2_0251_None: Coding = {
  code: 'N',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0251',
};
const v2_0251_Other: Coding = {
  code: 'OT',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0251',
};
const v2_0251_ProductWithdrawnPermanently: Coding = {
  code: 'WP',
  display: 'Product withdrawn permanently',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0251',
};
const v2_0251_ProductWithdrawnTemporarily: Coding = {
  code: 'WT',
  display: 'Product withdrawn temporarily',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0251',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0251 ( Action Taken in Response to the Event)
 */
export const V20251 = {
  ProductDoseOrFrequencyOfUseIncreased: v2_0251_ProductDoseOrFrequencyOfUseIncreased,
  ProductDoseOrFrequencyOfUseReduced: v2_0251_ProductDoseOrFrequencyOfUseReduced,
  None: v2_0251_None,
  Other: v2_0251_Other,
  ProductWithdrawnPermanently: v2_0251_ProductWithdrawnPermanently,
  ProductWithdrawnTemporarily: v2_0251_ProductWithdrawnTemporarily,
};
const v2_0252_AbatementOfEventAfterProductWithdrawn: Coding = {
  code: 'AW',
  display: 'Abatement of event after product withdrawn',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0252',
};
const v2_0252_EventRecurredAfterProductReintroduced: Coding = {
  code: 'BE',
  display: 'Event recurred after product reintroduced',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0252',
};
const v2_0252_DoseResponseObserved: Coding = {
  code: 'DR',
  display: 'Dose response observed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0252',
};
const v2_0252_AlternativeExplanationsForTheEventAvailable: Coding = {
  code: 'EX',
  display: 'Alternative explanations for the event available',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0252',
};
const v2_0252_EventOccurredAfterProductIntroduced: Coding = {
  code: 'IN',
  display: 'Event occurred after product introduced',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0252',
};
const v2_0252_LiteratureReportsAssociationOfProductWithEvent: Coding = {
  code: 'LI',
  display: 'Literature reports association of product with event',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0252',
};
const v2_0252_OccurrenceOfEventWasConfirmedByObjectiveEvidence: Coding = {
  code: 'OE',
  display: 'Occurrence of event was confirmed by objective evidence',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0252',
};
const v2_0252_Other: Coding = {
  code: 'OT',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0252',
};
const v2_0252_EffectObservedWhenPatientReceivesPlacebo: Coding = {
  code: 'PL',
  display: 'Effect observed when patient receives placebo',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0252',
};
const v2_0252_SimilarEventsInPastForThisPatient: Coding = {
  code: 'SE',
  display: 'Similar events in past for this patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0252',
};
const v2_0252_ToxicLevelsOfProductDocumentedInBloodOrBodyFluids: Coding = {
  code: 'TC',
  display: 'Toxic levels of product documented in blood or body fluids',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0252',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0252 ( Causality Observations)
 */
export const V20252 = {
  AbatementOfEventAfterProductWithdrawn: v2_0252_AbatementOfEventAfterProductWithdrawn,
  EventRecurredAfterProductReintroduced: v2_0252_EventRecurredAfterProductReintroduced,
  DoseResponseObserved: v2_0252_DoseResponseObserved,
  AlternativeExplanationsForTheEventAvailable: v2_0252_AlternativeExplanationsForTheEventAvailable,
  EventOccurredAfterProductIntroduced: v2_0252_EventOccurredAfterProductIntroduced,
  LiteratureReportsAssociationOfProductWithEvent: v2_0252_LiteratureReportsAssociationOfProductWithEvent,
  OccurrenceOfEventWasConfirmedByObjectiveEvidence: v2_0252_OccurrenceOfEventWasConfirmedByObjectiveEvidence,
  Other: v2_0252_Other,
  EffectObservedWhenPatientReceivesPlacebo: v2_0252_EffectObservedWhenPatientReceivesPlacebo,
  SimilarEventsInPastForThisPatient: v2_0252_SimilarEventsInPastForThisPatient,
  ToxicLevelsOfProductDocumentedInBloodOrBodyFluids: v2_0252_ToxicLevelsOfProductDocumentedInBloodOrBodyFluids,
};
const v2_0253_BreastMilk: Coding = {
  code: 'B',
  display: 'Breast milk',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0253',
};
const v2_0253_Father: Coding = {
  code: 'F',
  display: 'Father',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0253',
};
const v2_0253_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0253',
};
const v2_0253_Transplacental: Coding = {
  code: 'P',
  display: 'Transplacental',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0253',
};
const v2_0253_BloodProduct: Coding = {
  code: 'X',
  display: 'Blood product',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0253',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0253 ( Indirect Exposure Mechanism)
 */
export const V20253 = {
  BreastMilk: v2_0253_BreastMilk,
  Father: v2_0253_Father,
  Other: v2_0253_Other,
  Transplacental: v2_0253_Transplacental,
  BloodProduct: v2_0253_BloodProduct,
};
const v2_0254_Absorbance: Coding = {
  code: 'ABS',
  display: 'Absorbance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_ConcentrationArbitrarySubstance: Coding = {
  code: 'ACNC',
  display: 'Concentration, Arbitrary Substance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Activity: Coding = {
  code: 'ACT',
  display: 'Activity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Appearance: Coding = {
  code: 'APER',
  display: 'Appearance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Arbitrary: Coding = {
  code: 'ARB',
  display: 'Arbitrary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Area: Coding = {
  code: 'AREA',
  display: 'Area',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Aspect: Coding = {
  code: 'ASPECT',
  display: 'Aspect',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_CatalyticActivity: Coding = {
  code: 'CACT',
  display: 'Catalytic Activity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_CatalyticContent: Coding = {
  code: 'CCNT',
  display: 'Catalytic Content',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_CatalyticConcentrationRatio: Coding = {
  code: 'CCRTO',
  display: 'Catalytic Concentration Ratio',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_CatalyticFraction: Coding = {
  code: 'CFR',
  display: 'Catalytic Fraction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Class: Coding = {
  code: 'CLAS',
  display: 'Class',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_CatalyticConcentration: Coding = {
  code: 'CNC',
  display: 'Catalytic Concentration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Constant: Coding = {
  code: 'CNST',
  display: 'Constant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Coefficient: Coding = {
  code: 'COEF',
  display: 'Coefficient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Color: Coding = {
  code: 'COLOR',
  display: 'Color',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Consistency: Coding = {
  code: 'CONS',
  display: 'Consistency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_CatalyticRate: Coding = {
  code: 'CRAT',
  display: 'Catalytic Rate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_CatalyticRatio: Coding = {
  code: 'CRTO',
  display: 'Catalytic Ratio',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Density: Coding = {
  code: 'DEN',
  display: 'Density',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Device: Coding = {
  code: 'DEV',
  display: 'Device',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Difference: Coding = {
  code: 'DIFF',
  display: 'Difference',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Elasticity: Coding = {
  code: 'ELAS',
  display: 'Elasticity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_ElectricalPotentialVoltage: Coding = {
  code: 'ELPOT',
  display: 'Electrical Potential (Voltage)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_ElectricalCurrentAmperage: Coding = {
  code: 'ELRAT',
  display: 'Electrical current (amperage)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_ElectricalResistance: Coding = {
  code: 'ELRES',
  display: 'Electrical Resistance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Energy: Coding = {
  code: 'ENGR',
  display: 'Energy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Entitic: Coding = {
  code: 'ENT',
  display: 'Entitic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_EntiticCatalyticActivity: Coding = {
  code: 'ENTCAT',
  display: 'Entitic Catalytic Activity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_EntiticNumber: Coding = {
  code: 'ENTNUM',
  display: 'Entitic Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_EntiticSubstanceOfAmount: Coding = {
  code: 'ENTSUB',
  display: 'Entitic Substance of Amount',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_EntiticVolume: Coding = {
  code: 'ENTVOL',
  display: 'Entitic Volume',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Equilibrium: Coding = {
  code: 'EQL',
  display: 'Equilibrium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_MechanicalForce: Coding = {
  code: 'FORCE',
  display: 'Mechanical force',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Frequency: Coding = {
  code: 'FREQ',
  display: 'Frequency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_ImpressionInterpretationOfStudy: Coding = {
  code: 'IMP',
  display: 'Impression/ interpretation of study',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_KinematicViscosity: Coding = {
  code: 'KINV',
  display: 'Kinematic Viscosity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Length: Coding = {
  code: 'LEN',
  display: 'Length',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_LengthIncrement: Coding = {
  code: 'LINC',
  display: 'Length Increment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Liquefaction: Coding = {
  code: 'LIQ',
  display: 'Liquefaction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Mass: Coding = {
  code: 'MASS',
  display: 'Mass',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_MassConcentration: Coding = {
  code: 'MCNC',
  display: 'Mass Concentration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_MassContent: Coding = {
  code: 'MCNT',
  display: 'Mass Content',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_MassConcentrationRatio: Coding = {
  code: 'MCRTO',
  display: 'Mass Concentration Ratio',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_MassFraction: Coding = {
  code: 'MFR',
  display: 'Mass Fraction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_MagneticFlux: Coding = {
  code: 'MGFLUX',
  display: 'Magnetic flux',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_MassIncrement: Coding = {
  code: 'MINC',
  display: 'Mass Increment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Morphology: Coding = {
  code: 'MORPH',
  display: 'Morphology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Motility: Coding = {
  code: 'MOTIL',
  display: 'Motility',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_MassRate: Coding = {
  code: 'MRAT',
  display: 'Mass Rate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_MassRatio: Coding = {
  code: 'MRTO',
  display: 'Mass Ratio',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_NumberConcentration: Coding = {
  code: 'NCNC',
  display: 'Number Concentration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_NumberContent: Coding = {
  code: 'NCNT',
  display: 'Number Content',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_NumberFraction: Coding = {
  code: 'NFR',
  display: 'Number Fraction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_NumberRatio: Coding = {
  code: 'NRTO',
  display: 'Number Ratio',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Number: Coding = {
  code: 'NUM',
  display: 'Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_OpticalDensity: Coding = {
  code: 'OD',
  display: 'Optical density',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Osmolality: Coding = {
  code: 'OSMOL',
  display: 'Osmolality',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_PressurePartial: Coding = {
  code: 'PRES',
  display: 'Pressure (Partial)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_PresenceIdentityExistence: Coding = {
  code: 'PRID',
  display: 'Presence/Identity/Existence',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_PowerWattage: Coding = {
  code: 'PWR',
  display: 'Power (wattage)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Ranges: Coding = {
  code: 'RANGE',
  display: 'Ranges',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Ratios: Coding = {
  code: 'RATIO',
  display: 'Ratios',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_ReciprocalRelativeTime: Coding = {
  code: 'RCRLTM',
  display: 'Reciprocal Relative Time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_RelativeDensity: Coding = {
  code: 'RDEN',
  display: 'Relative Density',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Relative: Coding = {
  code: 'REL',
  display: 'Relative',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_RelativeMassConcentration: Coding = {
  code: 'RLMCNC',
  display: 'Relative Mass Concentration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_RelativeSubstanceConcentration: Coding = {
  code: 'RLSCNC',
  display: 'Relative Substance Concentration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_RelativeTime: Coding = {
  code: 'RLTM',
  display: 'Relative Time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_SaturationFraction: Coding = {
  code: 'SATFR',
  display: 'Saturation Fraction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_SubstanceConcentration: Coding = {
  code: 'SCNC',
  display: 'Substance Concentration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_SubstanceConcentrationIncrement: Coding = {
  code: 'SCNCIN',
  display: 'Substance Concentration Increment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_SubstanceContent: Coding = {
  code: 'SCNT',
  display: 'Substance Content',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_SubstanceContentRate: Coding = {
  code: 'SCNTR',
  display: 'Substance Content Rate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_SubstanceConcentrationRatio: Coding = {
  code: 'SCRTO',
  display: 'Substance Concentration Ratio',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_SubstanceFraction: Coding = {
  code: 'SFR',
  display: 'Substance Fraction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Shape: Coding = {
  code: 'SHAPE',
  display: 'Shape',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Smell: Coding = {
  code: 'SMELL',
  display: 'Smell',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_SubstanceRate: Coding = {
  code: 'SRAT',
  display: 'Substance Rate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_SubstanceRatio: Coding = {
  code: 'SRTO',
  display: 'Substance Ratio',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_SubstanceAmount: Coding = {
  code: 'SUB',
  display: 'Substance Amount',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Susceptibility: Coding = {
  code: 'SUSC',
  display: 'Susceptibility',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Taste: Coding = {
  code: 'TASTE',
  display: 'Taste',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Temperature: Coding = {
  code: 'TEMP',
  display: 'Temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_TemperatureDifference: Coding = {
  code: 'TEMPDF',
  display: 'Temperature Difference',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_TemperatureIncrement: Coding = {
  code: 'TEMPIN',
  display: 'Temperature Increment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_ThresholdMassConcentration: Coding = {
  code: 'THRMCNC',
  display: 'Threshold Mass Concentration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_ThresholdSubstanceConcentration: Coding = {
  code: 'THRSCNC',
  display: 'Threshold Substance Concentration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_TimeEGSeconds: Coding = {
  code: 'TIME',
  display: 'Time (e.g. seconds)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_DilutionFactorTiter: Coding = {
  code: 'TITR',
  display: 'Dilution Factor (Titer)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_TimeDifference: Coding = {
  code: 'TMDF',
  display: 'Time Difference',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_TimeStampDateAndTime: Coding = {
  code: 'TMSTP',
  display: 'Time Stamp-Date and Time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_TimeRatio: Coding = {
  code: 'TRTO',
  display: 'Time Ratio',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Type: Coding = {
  code: 'TYPE',
  display: 'Type',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_VolumeContent: Coding = {
  code: 'VCNT',
  display: 'Volume Content',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Velocity: Coding = {
  code: 'VEL',
  display: 'Velocity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_VelocityRatio: Coding = {
  code: 'VELRT',
  display: 'Velocity Ratio',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_VolumeFraction: Coding = {
  code: 'VFR',
  display: 'Volume Fraction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Viscosity: Coding = {
  code: 'VISC',
  display: 'Viscosity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_Volume: Coding = {
  code: 'VOL',
  display: 'Volume',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_VolumeRate: Coding = {
  code: 'VRAT',
  display: 'Volume Rate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
const v2_0254_VolumeRatio: Coding = {
  code: 'VRTO',
  display: 'Volume Ratio',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0254',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0254 ( Kind of Quantity)
 */
export const V20254 = {
  Absorbance: v2_0254_Absorbance,
  ConcentrationArbitrarySubstance: v2_0254_ConcentrationArbitrarySubstance,
  Activity: v2_0254_Activity,
  Appearance: v2_0254_Appearance,
  Arbitrary: v2_0254_Arbitrary,
  Area: v2_0254_Area,
  Aspect: v2_0254_Aspect,
  CatalyticActivity: v2_0254_CatalyticActivity,
  CatalyticContent: v2_0254_CatalyticContent,
  CatalyticConcentrationRatio: v2_0254_CatalyticConcentrationRatio,
  CatalyticFraction: v2_0254_CatalyticFraction,
  Class: v2_0254_Class,
  CatalyticConcentration: v2_0254_CatalyticConcentration,
  Constant: v2_0254_Constant,
  Coefficient: v2_0254_Coefficient,
  Color: v2_0254_Color,
  Consistency: v2_0254_Consistency,
  CatalyticRate: v2_0254_CatalyticRate,
  CatalyticRatio: v2_0254_CatalyticRatio,
  Density: v2_0254_Density,
  Device: v2_0254_Device,
  Difference: v2_0254_Difference,
  Elasticity: v2_0254_Elasticity,
  ElectricalPotentialVoltage: v2_0254_ElectricalPotentialVoltage,
  ElectricalCurrentAmperage: v2_0254_ElectricalCurrentAmperage,
  ElectricalResistance: v2_0254_ElectricalResistance,
  Energy: v2_0254_Energy,
  Entitic: v2_0254_Entitic,
  EntiticCatalyticActivity: v2_0254_EntiticCatalyticActivity,
  EntiticNumber: v2_0254_EntiticNumber,
  EntiticSubstanceOfAmount: v2_0254_EntiticSubstanceOfAmount,
  EntiticVolume: v2_0254_EntiticVolume,
  Equilibrium: v2_0254_Equilibrium,
  MechanicalForce: v2_0254_MechanicalForce,
  Frequency: v2_0254_Frequency,
  ImpressionInterpretationOfStudy: v2_0254_ImpressionInterpretationOfStudy,
  KinematicViscosity: v2_0254_KinematicViscosity,
  Length: v2_0254_Length,
  LengthIncrement: v2_0254_LengthIncrement,
  Liquefaction: v2_0254_Liquefaction,
  Mass: v2_0254_Mass,
  MassConcentration: v2_0254_MassConcentration,
  MassContent: v2_0254_MassContent,
  MassConcentrationRatio: v2_0254_MassConcentrationRatio,
  MassFraction: v2_0254_MassFraction,
  MagneticFlux: v2_0254_MagneticFlux,
  MassIncrement: v2_0254_MassIncrement,
  Morphology: v2_0254_Morphology,
  Motility: v2_0254_Motility,
  MassRate: v2_0254_MassRate,
  MassRatio: v2_0254_MassRatio,
  NumberConcentration: v2_0254_NumberConcentration,
  NumberContent: v2_0254_NumberContent,
  NumberFraction: v2_0254_NumberFraction,
  NumberRatio: v2_0254_NumberRatio,
  Number: v2_0254_Number,
  OpticalDensity: v2_0254_OpticalDensity,
  Osmolality: v2_0254_Osmolality,
  PressurePartial: v2_0254_PressurePartial,
  PresenceIdentityExistence: v2_0254_PresenceIdentityExistence,
  PowerWattage: v2_0254_PowerWattage,
  Ranges: v2_0254_Ranges,
  Ratios: v2_0254_Ratios,
  ReciprocalRelativeTime: v2_0254_ReciprocalRelativeTime,
  RelativeDensity: v2_0254_RelativeDensity,
  Relative: v2_0254_Relative,
  RelativeMassConcentration: v2_0254_RelativeMassConcentration,
  RelativeSubstanceConcentration: v2_0254_RelativeSubstanceConcentration,
  RelativeTime: v2_0254_RelativeTime,
  SaturationFraction: v2_0254_SaturationFraction,
  SubstanceConcentration: v2_0254_SubstanceConcentration,
  SubstanceConcentrationIncrement: v2_0254_SubstanceConcentrationIncrement,
  SubstanceContent: v2_0254_SubstanceContent,
  SubstanceContentRate: v2_0254_SubstanceContentRate,
  SubstanceConcentrationRatio: v2_0254_SubstanceConcentrationRatio,
  SubstanceFraction: v2_0254_SubstanceFraction,
  Shape: v2_0254_Shape,
  Smell: v2_0254_Smell,
  SubstanceRate: v2_0254_SubstanceRate,
  SubstanceRatio: v2_0254_SubstanceRatio,
  SubstanceAmount: v2_0254_SubstanceAmount,
  Susceptibility: v2_0254_Susceptibility,
  Taste: v2_0254_Taste,
  Temperature: v2_0254_Temperature,
  TemperatureDifference: v2_0254_TemperatureDifference,
  TemperatureIncrement: v2_0254_TemperatureIncrement,
  ThresholdMassConcentration: v2_0254_ThresholdMassConcentration,
  ThresholdSubstanceConcentration: v2_0254_ThresholdSubstanceConcentration,
  TimeEGSeconds: v2_0254_TimeEGSeconds,
  DilutionFactorTiter: v2_0254_DilutionFactorTiter,
  TimeDifference: v2_0254_TimeDifference,
  TimeStampDateAndTime: v2_0254_TimeStampDateAndTime,
  TimeRatio: v2_0254_TimeRatio,
  Type: v2_0254_Type,
  VolumeContent: v2_0254_VolumeContent,
  Velocity: v2_0254_Velocity,
  VelocityRatio: v2_0254_VelocityRatio,
  VolumeFraction: v2_0254_VolumeFraction,
  Viscosity: v2_0254_Viscosity,
  Volume: v2_0254_Volume,
  VolumeRate: v2_0254_VolumeRate,
  VolumeRatio: v2_0254_VolumeRatio,
};
const v2_0255_AsteriskLifeOfTheQuotationUnitQuotation: Coding = {
  code: '*',
  display: '(asterisk) Life of the "unit"',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL12Hours: Coding = {
  code: '12H',
  display: '12 hours',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL1Hour: Coding = {
  code: '1H',
  display: '1 hour',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL1Months30Days: Coding = {
  code: '1L',
  display: '1 months (30 days)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL1Week: Coding = {
  code: '1W',
  display: '1 week',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL212Hours: Coding = {
  code: '2.5H',
  display: '2 1/2 hours',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL24Hours: Coding = {
  code: '24H',
  display: '24 hours',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL2Days: Coding = {
  code: '2D',
  display: '2 days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL2Hours: Coding = {
  code: '2H',
  display: '2 hours',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL2Months: Coding = {
  code: '2L',
  display: '2 months',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL2Weeks: Coding = {
  code: '2W',
  display: '2 weeks',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL30Minutes: Coding = {
  code: '30M',
  display: '30 minutes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL3Days: Coding = {
  code: '3D',
  display: '3 days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL3Hours: Coding = {
  code: '3H',
  display: '3 hours',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL3Months: Coding = {
  code: '3L',
  display: '3 months',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL3Weeks: Coding = {
  code: '3W',
  display: '3 weeks',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL4Days: Coding = {
  code: '4D',
  display: '4 days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL4Hours: Coding = {
  code: '4H',
  display: '4 hours',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL4Weeks: Coding = {
  code: '4W',
  display: '4 weeks',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL5Days: Coding = {
  code: '5D',
  display: '5 days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL5Hours: Coding = {
  code: '5H',
  display: '5 hours',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL6Days: Coding = {
  code: '6D',
  display: '6 days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL6Hours: Coding = {
  code: '6H',
  display: '6 hours',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL7Hours: Coding = {
  code: '7H',
  display: '7 hours',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_VAL8Hours: Coding = {
  code: '8H',
  display: '8 hours',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_LifeOfTheQuotationUnitQuotation: Coding = {
  code: 'LU',
  display: 'Life of the "unit"',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
const v2_0255_PointInTime: Coding = {
  code: 'PT',
  display: 'point in time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0255',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0255 ( Duration Categories)
 */
export const V20255 = {
  AsteriskLifeOfTheQuotationUnitQuotation: v2_0255_AsteriskLifeOfTheQuotationUnitQuotation,
  VAL12Hours: v2_0255_VAL12Hours,
  VAL1Hour: v2_0255_VAL1Hour,
  VAL1Months30Days: v2_0255_VAL1Months30Days,
  VAL1Week: v2_0255_VAL1Week,
  VAL212Hours: v2_0255_VAL212Hours,
  VAL24Hours: v2_0255_VAL24Hours,
  VAL2Days: v2_0255_VAL2Days,
  VAL2Hours: v2_0255_VAL2Hours,
  VAL2Months: v2_0255_VAL2Months,
  VAL2Weeks: v2_0255_VAL2Weeks,
  VAL30Minutes: v2_0255_VAL30Minutes,
  VAL3Days: v2_0255_VAL3Days,
  VAL3Hours: v2_0255_VAL3Hours,
  VAL3Months: v2_0255_VAL3Months,
  VAL3Weeks: v2_0255_VAL3Weeks,
  VAL4Days: v2_0255_VAL4Days,
  VAL4Hours: v2_0255_VAL4Hours,
  VAL4Weeks: v2_0255_VAL4Weeks,
  VAL5Days: v2_0255_VAL5Days,
  VAL5Hours: v2_0255_VAL5Hours,
  VAL6Days: v2_0255_VAL6Days,
  VAL6Hours: v2_0255_VAL6Hours,
  VAL7Hours: v2_0255_VAL7Hours,
  VAL8Hours: v2_0255_VAL8Hours,
  LifeOfTheQuotationUnitQuotation: v2_0255_LifeOfTheQuotationUnitQuotation,
  PointInTime: v2_0255_PointInTime,
};
const v2_0256_VAL10Days: Coding = {
  code: '10D',
  display: '10 days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL10MinutesPostChallenge: Coding = {
  code: '10M',
  display: '10 minutes post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL12HoursPostChallenge: Coding = {
  code: '12H',
  display: '12 hours post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL15MinutesPostChallenge: Coding = {
  code: '15M',
  display: '15 minutes post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL1HourPostChallenge: Coding = {
  code: '1H',
  display: '1 hour post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL1Month30DaysPostChallenge: Coding = {
  code: '1L',
  display: '1 month (30 days) post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL1MinutePostChallenge: Coding = {
  code: '1M',
  display: '1 minute post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL1Week: Coding = {
  code: '1W',
  display: '1 week',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL212HoursPostChallenge: Coding = {
  code: '2.5H',
  display: '2 1/2 hours post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL20MinutesPostChallenge: Coding = {
  code: '20M',
  display: '20 minutes post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL24HoursPostChallenge: Coding = {
  code: '24H',
  display: '24 hours post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL25MinutesPostChallenge: Coding = {
  code: '25M',
  display: '25 minutes post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL2Days: Coding = {
  code: '2D',
  display: '2 days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL2HoursPostChallenge: Coding = {
  code: '2H',
  display: '2 hours post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL2Months60DaysPostChallenge: Coding = {
  code: '2L',
  display: '2 months (60 days) post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL2MinutesPostChallenge: Coding = {
  code: '2M',
  display: '2 minutes post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL2Weeks: Coding = {
  code: '2W',
  display: '2 weeks',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL30MinutesPostChallenge: Coding = {
  code: '30M',
  display: '30 minutes post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL3Days: Coding = {
  code: '3D',
  display: '3 days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL3HoursPostChallenge: Coding = {
  code: '3H',
  display: '3 hours post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL3Months90DaysPostChallenge: Coding = {
  code: '3L',
  display: '3 months (90 days) post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL3MinutesPostChallenge: Coding = {
  code: '3M',
  display: '3 minutes post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL3Weeks: Coding = {
  code: '3W',
  display: '3 weeks',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL4Days: Coding = {
  code: '4D',
  display: '4 days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL4HoursPostChallenge: Coding = {
  code: '4H',
  display: '4 hours post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL4MinutesPostChallenge: Coding = {
  code: '4M',
  display: '4 minutes post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL4Weeks: Coding = {
  code: '4W',
  display: '4 weeks',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL5Days: Coding = {
  code: '5D',
  display: '5 days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL5HoursPostChallenge: Coding = {
  code: '5H',
  display: '5  hours post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL5MinutesPostChallenge: Coding = {
  code: '5M',
  display: '5 minutes post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL6Days: Coding = {
  code: '6D',
  display: '6 days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL6HoursPostChallenge: Coding = {
  code: '6H',
  display: '6 hours post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL6MinutesPostChallenge: Coding = {
  code: '6M',
  display: '6 minutes post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL7Days: Coding = {
  code: '7D',
  display: '7 days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL7HoursPostChallenge: Coding = {
  code: '7H',
  display: '7 hours post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL7MinutesPostChallenge: Coding = {
  code: '7M',
  display: '7 minutes post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL8HoursPostChallenge: Coding = {
  code: '8H',
  display: '8 hours post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL8HoursAlignedOnNursingShifts: Coding = {
  code: '8H SHIFT',
  display: '8 hours aligned on nursing shifts',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL8MinutesPostChallenge: Coding = {
  code: '8M',
  display: '8 minutes post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_VAL9MinutesPostChallenge: Coding = {
  code: '9M',
  display: '9 minutes post challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_BaselineTimeJustBeforeTheChallenge: Coding = {
  code: 'BS',
  display: 'Baseline (time just before the challenge)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_TheTimePostDrugDoseAtWhichTheHighestDrugLevelIsReachedDiffersByDrug: Coding = {
  code: 'PEAK',
  display: 'The time post drug dose at which the highest drug level is reached (differs by drug)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_TimeFromTheChallengeOrDoseNotSpecifiedRandom: Coding = {
  code: 'RANDOM',
  display: 'Time from the challenge, or dose not specified. (random)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
const v2_0256_TheTimePostDrugDoseAtWhichTheLowestDrugLevelIsReachedVariesWithDrug: Coding = {
  code: 'TROUGH',
  display: 'The time post drug dose at which the lowest drug level is reached (varies with drug)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0256',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0256 ( Time Delay Post Challenge)
 */
export const V20256 = {
  VAL10Days: v2_0256_VAL10Days,
  VAL10MinutesPostChallenge: v2_0256_VAL10MinutesPostChallenge,
  VAL12HoursPostChallenge: v2_0256_VAL12HoursPostChallenge,
  VAL15MinutesPostChallenge: v2_0256_VAL15MinutesPostChallenge,
  VAL1HourPostChallenge: v2_0256_VAL1HourPostChallenge,
  VAL1Month30DaysPostChallenge: v2_0256_VAL1Month30DaysPostChallenge,
  VAL1MinutePostChallenge: v2_0256_VAL1MinutePostChallenge,
  VAL1Week: v2_0256_VAL1Week,
  VAL212HoursPostChallenge: v2_0256_VAL212HoursPostChallenge,
  VAL20MinutesPostChallenge: v2_0256_VAL20MinutesPostChallenge,
  VAL24HoursPostChallenge: v2_0256_VAL24HoursPostChallenge,
  VAL25MinutesPostChallenge: v2_0256_VAL25MinutesPostChallenge,
  VAL2Days: v2_0256_VAL2Days,
  VAL2HoursPostChallenge: v2_0256_VAL2HoursPostChallenge,
  VAL2Months60DaysPostChallenge: v2_0256_VAL2Months60DaysPostChallenge,
  VAL2MinutesPostChallenge: v2_0256_VAL2MinutesPostChallenge,
  VAL2Weeks: v2_0256_VAL2Weeks,
  VAL30MinutesPostChallenge: v2_0256_VAL30MinutesPostChallenge,
  VAL3Days: v2_0256_VAL3Days,
  VAL3HoursPostChallenge: v2_0256_VAL3HoursPostChallenge,
  VAL3Months90DaysPostChallenge: v2_0256_VAL3Months90DaysPostChallenge,
  VAL3MinutesPostChallenge: v2_0256_VAL3MinutesPostChallenge,
  VAL3Weeks: v2_0256_VAL3Weeks,
  VAL4Days: v2_0256_VAL4Days,
  VAL4HoursPostChallenge: v2_0256_VAL4HoursPostChallenge,
  VAL4MinutesPostChallenge: v2_0256_VAL4MinutesPostChallenge,
  VAL4Weeks: v2_0256_VAL4Weeks,
  VAL5Days: v2_0256_VAL5Days,
  VAL5HoursPostChallenge: v2_0256_VAL5HoursPostChallenge,
  VAL5MinutesPostChallenge: v2_0256_VAL5MinutesPostChallenge,
  VAL6Days: v2_0256_VAL6Days,
  VAL6HoursPostChallenge: v2_0256_VAL6HoursPostChallenge,
  VAL6MinutesPostChallenge: v2_0256_VAL6MinutesPostChallenge,
  VAL7Days: v2_0256_VAL7Days,
  VAL7HoursPostChallenge: v2_0256_VAL7HoursPostChallenge,
  VAL7MinutesPostChallenge: v2_0256_VAL7MinutesPostChallenge,
  VAL8HoursPostChallenge: v2_0256_VAL8HoursPostChallenge,
  VAL8HoursAlignedOnNursingShifts: v2_0256_VAL8HoursAlignedOnNursingShifts,
  VAL8MinutesPostChallenge: v2_0256_VAL8MinutesPostChallenge,
  VAL9MinutesPostChallenge: v2_0256_VAL9MinutesPostChallenge,
  BaselineTimeJustBeforeTheChallenge: v2_0256_BaselineTimeJustBeforeTheChallenge,
  TheTimePostDrugDoseAtWhichTheHighestDrugLevelIsReachedDiffersByDrug: v2_0256_TheTimePostDrugDoseAtWhichTheHighestDrugLevelIsReachedDiffersByDrug,
  TimeFromTheChallengeOrDoseNotSpecifiedRandom: v2_0256_TimeFromTheChallengeOrDoseNotSpecifiedRandom,
  TheTimePostDrugDoseAtWhichTheLowestDrugLevelIsReachedVariesWithDrug: v2_0256_TheTimePostDrugDoseAtWhichTheLowestDrugLevelIsReachedVariesWithDrug,
};
const v2_0257_FastingNoCalorieIntakeForThePeriodSpecifiedInTheTimeComponentOfTheTermEG1HPOSTCFST: Coding = {
  code: 'CFST',
  display: 'Fasting (no calorie intake) for the period specified in the time component of the term, e.g., 1H POST CFST',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0257',
};
const v2_0257_ExerciseUndertakenAsChallengeCanBeQuantified: Coding = {
  code: 'EXCZ',
  display: 'Exercise undertaken as challenge (can be quantified)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0257',
};
const v2_0257_NoFluidIntakeForThePeriodSpecifiedInTheTimeComponentOfTheTerm: Coding = {
  code: 'FFST',
  display: 'No fluid intake for the period specified in the time component of the term',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0257',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0257 ( Nature of Challenge)
 */
export const V20257 = {
  FastingNoCalorieIntakeForThePeriodSpecifiedInTheTimeComponentOfTheTermEG1HPOSTCFST: v2_0257_FastingNoCalorieIntakeForThePeriodSpecifiedInTheTimeComponentOfTheTermEG1HPOSTCFST,
  ExerciseUndertakenAsChallengeCanBeQuantified: v2_0257_ExerciseUndertakenAsChallengeCanBeQuantified,
  NoFluidIntakeForThePeriodSpecifiedInTheTimeComponentOfTheTerm: v2_0257_NoFluidIntakeForThePeriodSpecifiedInTheTimeComponentOfTheTerm,
};
const v2_0258_BloodProductUnit: Coding = {
  code: 'BPU',
  display: 'Blood product unit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0258',
};
const v2_0258_Control: Coding = {
  code: 'CONTROL',
  display: 'Control',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0258',
};
const v2_0258_Donor: Coding = {
  code: 'DONOR',
  display: 'Donor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0258',
};
const v2_0258_Patient: Coding = {
  code: 'PATIENT',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0258',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0258 ( Relationship Modifier)
 */
export const V20258 = {
  BloodProductUnit: v2_0258_BloodProductUnit,
  Control: v2_0258_Control,
  Donor: v2_0258_Donor,
  Patient: v2_0258_Patient,
};
const v2_0260_Bed: Coding = {
  code: 'B',
  display: 'Bed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0260',
};
const v2_0260_Clinic: Coding = {
  code: 'C',
  display: 'Clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0260',
};
const v2_0260_Department: Coding = {
  code: 'D',
  display: 'Department',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0260',
};
const v2_0260_ExamRoom: Coding = {
  code: 'E',
  display: 'Exam Room',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0260',
};
const v2_0260_OtherLocation: Coding = {
  code: 'L',
  display: 'Other Location',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0260',
};
const v2_0260_NursingUnit: Coding = {
  code: 'N',
  display: 'Nursing Unit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0260',
};
const v2_0260_OperatingRoom: Coding = {
  code: 'O',
  display: 'Operating Room',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0260',
};
const v2_0260_Room: Coding = {
  code: 'R',
  display: 'Room',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0260',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0260 ( Patient Location Type)
 */
export const V20260 = {
  Bed: v2_0260_Bed,
  Clinic: v2_0260_Clinic,
  Department: v2_0260_Department,
  ExamRoom: v2_0260_ExamRoom,
  OtherLocation: v2_0260_OtherLocation,
  NursingUnit: v2_0260_NursingUnit,
  OperatingRoom: v2_0260_OperatingRoom,
  Room: v2_0260_Room,
};
const v2_0261_ElectroEncephalogram: Coding = {
  code: 'EEG',
  display: 'Electro-Encephalogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0261',
};
const v2_0261_ElectroCardiogram: Coding = {
  code: 'EKG',
  display: 'Electro-Cardiogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0261',
};
const v2_0261_InfusionPump: Coding = {
  code: 'INF',
  display: 'Infusion pump',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0261',
};
const v2_0261_IVPump: Coding = {
  code: 'IVP',
  display: 'IV pump',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0261',
};
const v2_0261_Oxygen: Coding = {
  code: 'OXY',
  display: 'Oxygen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0261',
};
const v2_0261_Suction: Coding = {
  code: 'SUC',
  display: 'Suction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0261',
};
const v2_0261_Ventilator: Coding = {
  code: 'VEN',
  display: 'Ventilator',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0261',
};
const v2_0261_VitalSignsMonitor: Coding = {
  code: 'VIT',
  display: 'Vital signs monitor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0261',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0261 ( Location Equipment)
 */
export const V20261 = {
  ElectroEncephalogram: v2_0261_ElectroEncephalogram,
  ElectroCardiogram: v2_0261_ElectroCardiogram,
  InfusionPump: v2_0261_InfusionPump,
  IVPump: v2_0261_IVPump,
  Oxygen: v2_0261_Oxygen,
  Suction: v2_0261_Suction,
  Ventilator: v2_0261_Ventilator,
  VitalSignsMonitor: v2_0261_VitalSignsMonitor,
};
const v2_0262_Isolation: Coding = {
  code: 'F',
  display: 'Isolation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0262',
};
const v2_0262_PrivateRoomMedicallyJustified: Coding = {
  code: 'J',
  display: 'Private room - medically justified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0262',
};
const v2_0262_PrivateRoom: Coding = {
  code: 'P',
  display: 'Private room',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0262',
};
const v2_0262_PrivateRoomDueToOverflow: Coding = {
  code: 'Q',
  display: 'Private room - due to overflow',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0262',
};
const v2_0262_SemiPrivateRoom: Coding = {
  code: 'S',
  display: 'Semi-private room',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0262',
};
const v2_0262_Ward: Coding = {
  code: 'W',
  display: 'Ward',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0262',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0262 ( Privacy Level)
 */
export const V20262 = {
  Isolation: v2_0262_Isolation,
  PrivateRoomMedicallyJustified: v2_0262_PrivateRoomMedicallyJustified,
  PrivateRoom: v2_0262_PrivateRoom,
  PrivateRoomDueToOverflow: v2_0262_PrivateRoomDueToOverflow,
  SemiPrivateRoom: v2_0262_SemiPrivateRoom,
  Ward: v2_0262_Ward,
};
const v2_0263_Ambulatory: Coding = {
  code: 'A',
  display: 'Ambulatory',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0263',
};
const v2_0263_CriticalCare: Coding = {
  code: 'C',
  display: 'Critical care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0263',
};
const v2_0263_Emergency: Coding = {
  code: 'E',
  display: 'Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0263',
};
const v2_0263_Isolation: Coding = {
  code: 'F',
  display: 'Isolation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0263',
};
const v2_0263_IntensiveCare: Coding = {
  code: 'N',
  display: 'Intensive care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0263',
};
const v2_0263_Routine: Coding = {
  code: 'R',
  display: 'Routine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0263',
};
const v2_0263_Surgery: Coding = {
  code: 'S',
  display: 'Surgery',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0263',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0263 ( Level of Care)
 */
export const V20263 = {
  Ambulatory: v2_0263_Ambulatory,
  CriticalCare: v2_0263_CriticalCare,
  Emergency: v2_0263_Emergency,
  Isolation: v2_0263_Isolation,
  IntensiveCare: v2_0263_IntensiveCare,
  Routine: v2_0263_Routine,
  Surgery: v2_0263_Surgery,
};
const v2_0265_Allergy: Coding = {
  code: 'ALC',
  display: 'Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_Ambulatory: Coding = {
  code: 'AMB',
  display: 'Ambulatory',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_Cancer: Coding = {
  code: 'CAN',
  display: 'Cancer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_CoronaryCardiacCare: Coding = {
  code: 'CAR',
  display: 'Coronary/cardiac care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_CriticalCare: Coding = {
  code: 'CCR',
  display: 'Critical care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_Chiropractic: Coding = {
  code: 'CHI',
  display: 'Chiropractic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_Education: Coding = {
  code: 'EDI',
  display: 'Education',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_Emergency: Coding = {
  code: 'EMR',
  display: 'Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_FamilyPlanning: Coding = {
  code: 'FPC',
  display: 'Family planning',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_IntensiveCare: Coding = {
  code: 'INT',
  display: 'Intensive care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_Isolation: Coding = {
  code: 'ISO',
  display: 'Isolation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_Naturopathic: Coding = {
  code: 'NAT',
  display: 'Naturopathic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_NewbornNurseryInfants: Coding = {
  code: 'NBI',
  display: 'Newborn, nursery, infants',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_ObstetricsGynecology: Coding = {
  code: 'OBG',
  display: 'Obstetrics, gynecology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_Observation: Coding = {
  code: 'OBS',
  display: 'Observation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_OtherSpecialty: Coding = {
  code: 'OTH',
  display: 'Other specialty',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_Pediatrics: Coding = {
  code: 'PED',
  display: 'Pediatrics',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_GeneralFamilyPractice: Coding = {
  code: 'PHY',
  display: 'General/family practice',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_PediatricNeonatalIntensiveCare: Coding = {
  code: 'PIN',
  display: 'Pediatric/neonatal intensive care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_PediatricPsychiatric: Coding = {
  code: 'PPS',
  display: 'Pediatric psychiatric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_PediatricRehabilitation: Coding = {
  code: 'PRE',
  display: 'Pediatric rehabilitation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_PsychiatricIntensiveCare: Coding = {
  code: 'PSI',
  display: 'Psychiatric intensive care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_Psychiatric: Coding = {
  code: 'PSY',
  display: 'Psychiatric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_Rehabilitation: Coding = {
  code: 'REH',
  display: 'Rehabilitation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_Surgery: Coding = {
  code: 'SUR',
  display: 'Surgery',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
const v2_0265_WalkInClinic: Coding = {
  code: 'WIC',
  display: 'Walk-in clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0265',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0265 ( Specialty Type)
 */
export const V20265 = {
  Allergy: v2_0265_Allergy,
  Ambulatory: v2_0265_Ambulatory,
  Cancer: v2_0265_Cancer,
  CoronaryCardiacCare: v2_0265_CoronaryCardiacCare,
  CriticalCare: v2_0265_CriticalCare,
  Chiropractic: v2_0265_Chiropractic,
  Education: v2_0265_Education,
  Emergency: v2_0265_Emergency,
  FamilyPlanning: v2_0265_FamilyPlanning,
  IntensiveCare: v2_0265_IntensiveCare,
  Isolation: v2_0265_Isolation,
  Naturopathic: v2_0265_Naturopathic,
  NewbornNurseryInfants: v2_0265_NewbornNurseryInfants,
  ObstetricsGynecology: v2_0265_ObstetricsGynecology,
  Observation: v2_0265_Observation,
  OtherSpecialty: v2_0265_OtherSpecialty,
  Pediatrics: v2_0265_Pediatrics,
  GeneralFamilyPractice: v2_0265_GeneralFamilyPractice,
  PediatricNeonatalIntensiveCare: v2_0265_PediatricNeonatalIntensiveCare,
  PediatricPsychiatric: v2_0265_PediatricPsychiatric,
  PediatricRehabilitation: v2_0265_PediatricRehabilitation,
  PsychiatricIntensiveCare: v2_0265_PsychiatricIntensiveCare,
  Psychiatric: v2_0265_Psychiatric,
  Rehabilitation: v2_0265_Rehabilitation,
  Surgery: v2_0265_Surgery,
  WalkInClinic: v2_0265_WalkInClinic,
};
const v2_0267_Friday: Coding = {
  code: 'FRI',
  display: 'Friday',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0267',
};
const v2_0267_Monday: Coding = {
  code: 'MON',
  display: 'Monday',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0267',
};
const v2_0267_Saturday: Coding = {
  code: 'SAT',
  display: 'Saturday',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0267',
};
const v2_0267_Sunday: Coding = {
  code: 'SUN',
  display: 'Sunday',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0267',
};
const v2_0267_Thursday: Coding = {
  code: 'THU',
  display: 'Thursday',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0267',
};
const v2_0267_Tuesday: Coding = {
  code: 'TUE',
  display: 'Tuesday',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0267',
};
const v2_0267_Wednesday: Coding = {
  code: 'WED',
  display: 'Wednesday',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0267',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0267 ( Days of the Week)
 */
export const V20267 = {
  Friday: v2_0267_Friday,
  Monday: v2_0267_Monday,
  Saturday: v2_0267_Saturday,
  Sunday: v2_0267_Sunday,
  Thursday: v2_0267_Thursday,
  Tuesday: v2_0267_Tuesday,
  Wednesday: v2_0267_Wednesday,
};
const v2_0268_OverrideAllowed: Coding = {
  code: 'A',
  display: 'Override allowed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0268',
};
const v2_0268_OverrideRequired: Coding = {
  code: 'R',
  display: 'Override required',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0268',
};
const v2_0268_OverrideNotAllowed: Coding = {
  code: 'X',
  display: 'Override not allowed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0268',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0268 ( Override)
 */
export const V20268 = {
  OverrideAllowed: v2_0268_OverrideAllowed,
  OverrideRequired: v2_0268_OverrideRequired,
  OverrideNotAllowed: v2_0268_OverrideNotAllowed,
};
const v2_0269_ChargeOnOrder: Coding = {
  code: 'O',
  display: 'Charge on Order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0269',
};
const v2_0269_ChargeOnResult: Coding = {
  code: 'R',
  display: 'Charge on Result',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0269',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0269 ( Charge on Indicator)
 */
export const V20269 = {
  ChargeOnOrder: v2_0269_ChargeOnOrder,
  ChargeOnResult: v2_0269_ChargeOnResult,
};
const v2_0270_AutopsyReport: Coding = {
  code: 'AR',
  display: 'Autopsy report',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
const v2_0270_Cardiodiagnostics: Coding = {
  code: 'CD',
  display: 'Cardiodiagnostics',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
const v2_0270_Consultation: Coding = {
  code: 'CN',
  display: 'Consultation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
const v2_0270_DiagnosticImaging: Coding = {
  code: 'DI',
  display: 'Diagnostic imaging',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
const v2_0270_DischargeSummary: Coding = {
  code: 'DS',
  display: 'Discharge summary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
const v2_0270_EmergencyDepartmentReport: Coding = {
  code: 'ED',
  display: 'Emergency department report',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
const v2_0270_HistoryAndPhysicalExamination: Coding = {
  code: 'HP',
  display: 'History and physical examination',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
const v2_0270_OperativeReport: Coding = {
  code: 'OP',
  display: 'Operative report',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
const v2_0270_PsychiatricConsultation: Coding = {
  code: 'PC',
  display: 'Psychiatric consultation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
const v2_0270_PsychiatricHistoryAndPhysicalExamination: Coding = {
  code: 'PH',
  display: 'Psychiatric history and physical examination',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
const v2_0270_ProcedureNote: Coding = {
  code: 'PN',
  display: 'Procedure note',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
const v2_0270_ProgressNote: Coding = {
  code: 'PR',
  display: 'Progress note',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
const v2_0270_SurgicalPathology: Coding = {
  code: 'SP',
  display: 'Surgical pathology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
const v2_0270_TransferSummary: Coding = {
  code: 'TS',
  display: 'Transfer summary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0270',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0270 ( Report Type Code)
 */
export const V20270 = {
  AutopsyReport: v2_0270_AutopsyReport,
  Cardiodiagnostics: v2_0270_Cardiodiagnostics,
  Consultation: v2_0270_Consultation,
  DiagnosticImaging: v2_0270_DiagnosticImaging,
  DischargeSummary: v2_0270_DischargeSummary,
  EmergencyDepartmentReport: v2_0270_EmergencyDepartmentReport,
  HistoryAndPhysicalExamination: v2_0270_HistoryAndPhysicalExamination,
  OperativeReport: v2_0270_OperativeReport,
  PsychiatricConsultation: v2_0270_PsychiatricConsultation,
  PsychiatricHistoryAndPhysicalExamination: v2_0270_PsychiatricHistoryAndPhysicalExamination,
  ProcedureNote: v2_0270_ProcedureNote,
  ProgressNote: v2_0270_ProgressNote,
  SurgicalPathology: v2_0270_SurgicalPathology,
  TransferSummary: v2_0270_TransferSummary,
};
const v2_0271_Authenticated: Coding = {
  code: 'AU',
  display: 'Authenticated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0271',
};
const v2_0271_Dictated: Coding = {
  code: 'DI',
  display: 'Dictated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0271',
};
const v2_0271_Documented: Coding = {
  code: 'DO',
  display: 'Documented',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0271',
};
const v2_0271_Incomplete: Coding = {
  code: 'IN',
  display: 'Incomplete',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0271',
};
const v2_0271_InProgress: Coding = {
  code: 'IP',
  display: 'In Progress',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0271',
};
const v2_0271_LegallyAuthenticated: Coding = {
  code: 'LA',
  display: 'Legally authenticated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0271',
};
const v2_0271_PreAuthenticated: Coding = {
  code: 'PA',
  display: 'Pre-authenticated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0271',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0271 ( Document Completion Status)
 */
export const V20271 = {
  Authenticated: v2_0271_Authenticated,
  Dictated: v2_0271_Dictated,
  Documented: v2_0271_Documented,
  Incomplete: v2_0271_Incomplete,
  InProgress: v2_0271_InProgress,
  LegallyAuthenticated: v2_0271_LegallyAuthenticated,
  PreAuthenticated: v2_0271_PreAuthenticated,
};
const v2_0272_Restricted: Coding = {
  code: 'R',
  display: 'Restricted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0272',
};
const v2_0272_UsualControl: Coding = {
  code: 'U',
  display: 'Usual control',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0272',
};
const v2_0272_VeryRestricted: Coding = {
  code: 'V',
  display: 'Very restricted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0272',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0272 ( Document Confidentiality Status)
 */
export const V20272 = {
  Restricted: v2_0272_Restricted,
  UsualControl: v2_0272_UsualControl,
  VeryRestricted: v2_0272_VeryRestricted,
};
const v2_0273_AvailableForPatientCare: Coding = {
  code: 'AV',
  display: 'Available for patient care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0273',
};
const v2_0273_Deleted: Coding = {
  code: 'CA',
  display: 'Deleted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0273',
};
const v2_0273_Obsolete: Coding = {
  code: 'OB',
  display: 'Obsolete',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0273',
};
const v2_0273_UnavailableForPatientCare: Coding = {
  code: 'UN',
  display: 'Unavailable for patient care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0273',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0273 ( Document Availability Status)
 */
export const V20273 = {
  AvailableForPatientCare: v2_0273_AvailableForPatientCare,
  Deleted: v2_0273_Deleted,
  Obsolete: v2_0273_Obsolete,
  UnavailableForPatientCare: v2_0273_UnavailableForPatientCare,
};
const v2_0275_ActiveAndArchived: Coding = {
  code: 'AA',
  display: 'Active and archived',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0275',
};
const v2_0275_Active: Coding = {
  code: 'AC',
  display: 'Active',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0275',
};
const v2_0275_ArchivedNotActive: Coding = {
  code: 'AR',
  display: 'Archived (not active)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0275',
};
const v2_0275_Purged: Coding = {
  code: 'PU',
  display: 'Purged',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0275',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0275 ( Document Storage Status)
 */
export const V20275 = {
  ActiveAndArchived: v2_0275_ActiveAndArchived,
  Active: v2_0275_Active,
  ArchivedNotActive: v2_0275_ArchivedNotActive,
  Purged: v2_0275_Purged,
};
const v2_0276_ARoutineCheckUpSuchAsAnAnnualPhysical: Coding = {
  code: 'CHECKUP',
  display: 'A routine check-up, such as an annual physical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0276',
};
const v2_0276_EmergencyAppointment: Coding = {
  code: 'EMERGENCY',
  display: 'Emergency appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0276',
};
const v2_0276_AFollowUpVisitFromAPreviousAppointment: Coding = {
  code: 'FOLLOWUP',
  display: 'A follow up visit from a previous appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0276',
};
const v2_0276_RoutineAppointmentDefaultIfNotValued: Coding = {
  code: 'ROUTINE',
  display: 'Routine appointment - default if not valued',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0276',
};
const v2_0276_APreviouslyUnscheduledWalkInVisit: Coding = {
  code: 'WALKIN',
  display: 'A previously unscheduled walk-in visit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0276',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0276 ( Appointment Reason Codes)
 */
export const V20276 = {
  ARoutineCheckUpSuchAsAnAnnualPhysical: v2_0276_ARoutineCheckUpSuchAsAnAnnualPhysical,
  EmergencyAppointment: v2_0276_EmergencyAppointment,
  AFollowUpVisitFromAPreviousAppointment: v2_0276_AFollowUpVisitFromAPreviousAppointment,
  RoutineAppointmentDefaultIfNotValued: v2_0276_RoutineAppointmentDefaultIfNotValued,
  APreviouslyUnscheduledWalkInVisit: v2_0276_APreviouslyUnscheduledWalkInVisit,
};
const v2_0277_Complete: Coding = {
  code: 'Complete',
  display:
    'A request to add a completed appointment, used to maintain records of completed appointments that did not appear in the schedule (e.g., STAT, walk-in, etc.)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0277',
};
const v2_0277_Normal: Coding = {
  code: 'Normal',
  display: 'Routine schedule request type – default if not valued',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0277',
};
const v2_0277_Tentative: Coding = {
  code: 'Tentative',
  display: 'A request for a tentative (e.g., “penciled in”) appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0277',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0277 ( Appointment Type Codes)
 */
export const V20277 = {
  Complete: v2_0277_Complete,
  Normal: v2_0277_Normal,
  Tentative: v2_0277_Tentative,
};
const v2_0278_Blocked: Coding = {
  code: 'Blocked',
  display: 'Blocked',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0278',
};
const v2_0278_Booked: Coding = {
  code: 'Booked',
  display: 'Booked',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0278',
};
const v2_0278_Cancelled: Coding = {
  code: 'Cancelled',
  display: 'Cancelled',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0278',
};
const v2_0278_Complete: Coding = {
  code: 'Complete',
  display: 'Complete',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0278',
};
const v2_0278_Discontinued: Coding = {
  code: 'DC',
  display: 'Discontinued',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0278',
};
const v2_0278_Deleted: Coding = {
  code: 'Deleted',
  display: 'Deleted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0278',
};
const v2_0278_Discontinued_2: Coding = {
  code: 'Discontinued',
  display: 'Discontinued',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0278',
};
const v2_0278_Noshow: Coding = {
  code: 'Noshow',
  display: 'Noshow',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0278',
};
const v2_0278_Overbook: Coding = {
  code: 'Overbook',
  display: 'Overbook',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0278',
};
const v2_0278_Pending: Coding = {
  code: 'Pending',
  display: 'Pending',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0278',
};
const v2_0278_Started: Coding = {
  code: 'Started',
  display: 'Started',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0278',
};
const v2_0278_Waitlist: Coding = {
  code: 'Waitlist',
  display: 'Waitlist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0278',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0278 ( Filler Status Codes)
 */
export const V20278 = {
  Blocked: v2_0278_Blocked,
  Booked: v2_0278_Booked,
  Cancelled: v2_0278_Cancelled,
  Complete: v2_0278_Complete,
  Discontinued: v2_0278_Discontinued,
  Deleted: v2_0278_Deleted,
  Discontinued_2: v2_0278_Discontinued_2,
  Noshow: v2_0278_Noshow,
  Overbook: v2_0278_Overbook,
  Pending: v2_0278_Pending,
  Started: v2_0278_Started,
  Waitlist: v2_0278_Waitlist,
};
const v2_0279_ContactThePlacerContactPersonPriorToMakingAnySubstitutionsOfThisResource: Coding = {
  code: 'Confirm',
  display: 'Contact the Placer Contact Person prior to making any substitutions of this resource',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0279',
};
const v2_0279_SubstitutionOfThisResourceIsNotAllowed: Coding = {
  code: 'No',
  display: 'Substitution of this resource is not allowed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0279',
};
const v2_0279_NotifyThePlacerContactPersonThroughNormalInstitutionalProceduresThatASubstitutionOfThisResourceHasBeenMade: Coding = {
  code: 'Notify',
  display: 'Notify the Placer Contact Person, through normal institutional procedures, that a substitution of this resource has been made',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0279',
};
const v2_0279_SubstitutionOfThisResourceIsAllowed: Coding = {
  code: 'Yes',
  display: 'Substitution of this resource is allowed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0279',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0279 ( Allow Substitution Codes)
 */
export const V20279 = {
  ContactThePlacerContactPersonPriorToMakingAnySubstitutionsOfThisResource: v2_0279_ContactThePlacerContactPersonPriorToMakingAnySubstitutionsOfThisResource,
  SubstitutionOfThisResourceIsNotAllowed: v2_0279_SubstitutionOfThisResourceIsNotAllowed,
  NotifyThePlacerContactPersonThroughNormalInstitutionalProceduresThatASubstitutionOfThisResourceHasBeenMade: v2_0279_NotifyThePlacerContactPersonThroughNormalInstitutionalProceduresThatASubstitutionOfThisResourceHasBeenMade,
  SubstitutionOfThisResourceIsAllowed: v2_0279_SubstitutionOfThisResourceIsAllowed,
};
const v2_0280_ASAP: Coding = {
  code: 'A',
  display: 'ASAP',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0280',
};
const v2_0280_Routine: Coding = {
  code: 'R',
  display: 'Routine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0280',
};
const v2_0280_STAT: Coding = {
  code: 'S',
  display: 'STAT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0280',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0280 ( Referral Priority)
 */
export const V20280 = {
  ASAP: v2_0280_ASAP,
  Routine: v2_0280_Routine,
  STAT: v2_0280_STAT,
};
const v2_0281_HomeCare: Coding = {
  code: 'Hom',
  display: 'Home Care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0281',
};
const v2_0281_Laboratory: Coding = {
  code: 'Lab',
  display: 'Laboratory',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0281',
};
const v2_0281_Medical: Coding = {
  code: 'Med',
  display: 'Medical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0281',
};
const v2_0281_Psychiatric: Coding = {
  code: 'Psy',
  display: 'Psychiatric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0281',
};
const v2_0281_Radiology: Coding = {
  code: 'Rad',
  display: 'Radiology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0281',
};
const v2_0281_SkilledNursing: Coding = {
  code: 'Skn',
  display: 'Skilled Nursing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0281',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0281 ( Referral Type)
 */
export const V20281 = {
  HomeCare: v2_0281_HomeCare,
  Laboratory: v2_0281_Laboratory,
  Medical: v2_0281_Medical,
  Psychiatric: v2_0281_Psychiatric,
  Radiology: v2_0281_Radiology,
  SkilledNursing: v2_0281_SkilledNursing,
};
const v2_0282_AssumeManagement: Coding = {
  code: 'AM',
  display: 'Assume Management',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0282',
};
const v2_0282_ReturnPatientAfterEvaluation: Coding = {
  code: 'RP',
  display: 'Return Patient After Evaluation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0282',
};
const v2_0282_SecondOpinion: Coding = {
  code: 'SO',
  display: 'Second Opinion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0282',
};
const v2_0282_SendWrittenReport: Coding = {
  code: 'WR',
  display: 'Send Written Report',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0282',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0282 ( Referral Disposition)
 */
export const V20282 = {
  AssumeManagement: v2_0282_AssumeManagement,
  ReturnPatientAfterEvaluation: v2_0282_ReturnPatientAfterEvaluation,
  SecondOpinion: v2_0282_SecondOpinion,
  SendWrittenReport: v2_0282_SendWrittenReport,
};
const v2_0283_Accepted: Coding = {
  code: 'A',
  display: 'Accepted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0283',
};
const v2_0283_Expired: Coding = {
  code: 'E',
  display: 'Expired',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0283',
};
const v2_0283_Pending: Coding = {
  code: 'P',
  display: 'Pending',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0283',
};
const v2_0283_Rejected: Coding = {
  code: 'R',
  display: 'Rejected',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0283',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0283 ( Referral Status)
 */
export const V20283 = {
  Accepted: v2_0283_Accepted,
  Expired: v2_0283_Expired,
  Pending: v2_0283_Pending,
  Rejected: v2_0283_Rejected,
};
const v2_0284_Ambulatory: Coding = {
  code: 'A',
  display: 'Ambulatory',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0284',
};
const v2_0284_Emergency: Coding = {
  code: 'E',
  display: 'Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0284',
};
const v2_0284_Inpatient: Coding = {
  code: 'I',
  display: 'Inpatient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0284',
};
const v2_0284_Outpatient: Coding = {
  code: 'O',
  display: 'Outpatient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0284',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0284 ( Referral Category)
 */
export const V20284 = {
  Ambulatory: v2_0284_Ambulatory,
  Emergency: v2_0284_Emergency,
  Inpatient: v2_0284_Inpatient,
  Outpatient: v2_0284_Outpatient,
};
const v2_0286_ConsultingProvider: Coding = {
  code: 'CP',
  display: 'Consulting Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0286',
};
const v2_0286_PrimaryCareProvider: Coding = {
  code: 'PP',
  display: 'Primary Care Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0286',
};
const v2_0286_ReferringProvider: Coding = {
  code: 'RP',
  display: 'Referring Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0286',
};
const v2_0286_ReferredToProvider: Coding = {
  code: 'RT',
  display: 'Referred to Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0286',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0286 ( Provider Role)
 */
export const V20286 = {
  ConsultingProvider: v2_0286_ConsultingProvider,
  PrimaryCareProvider: v2_0286_PrimaryCareProvider,
  ReferringProvider: v2_0286_ReferringProvider,
  ReferredToProvider: v2_0286_ReferredToProvider,
};
const v2_0287_ADD: Coding = {
  code: 'AD',
  display: 'ADD',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0287',
};
const v2_0287_CORRECT: Coding = {
  code: 'CO',
  display: 'CORRECT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0287',
};
const v2_0287_DELETE: Coding = {
  code: 'DE',
  display: 'DELETE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0287',
};
const v2_0287_LINK: Coding = {
  code: 'LI',
  display: 'LINK',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0287',
};
const v2_0287_UsedInSnapshotMode: Coding = {
  code: 'SP',
  display: 'Used in Snapshot mode',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0287',
};
const v2_0287_UNCHANGED: Coding = {
  code: 'UC',
  display: 'UNCHANGED',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0287',
};
const v2_0287_UNLINK: Coding = {
  code: 'UN',
  display: 'UNLINK',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0287',
};
const v2_0287_UPDATE: Coding = {
  code: 'UP',
  display: 'UPDATE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0287',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0287 ( Action Code)
 */
export const V20287 = {
  ADD: v2_0287_ADD,
  CORRECT: v2_0287_CORRECT,
  DELETE: v2_0287_DELETE,
  LINK: v2_0287_LINK,
  UsedInSnapshotMode: v2_0287_UsedInSnapshotMode,
  UNCHANGED: v2_0287_UNCHANGED,
  UNLINK: v2_0287_UNLINK,
  UPDATE: v2_0287_UPDATE,
};
const v2_0291_ISDNPCMAudioData: Coding = {
  code: 'BASIC',
  display: 'ISDN PCM audio data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_DigitalImagingAndCommunicationsInMedicine: Coding = {
  code: 'DICOM',
  display: 'Digital Imaging and Communications in Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_FacsimileData: Coding = {
  code: 'FAX',
  display: 'Facsimile data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_GraphicsInterchangeFormat: Coding = {
  code: 'GIF',
  display: 'Graphics Interchange Format',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_HypertextMarkupLanguage: Coding = {
  code: 'HTML',
  display: 'Hypertext Markup Language',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_ElectronicInkDataJot10Standard: Coding = {
  code: 'JOT',
  display: 'Electronic ink data (Jot 1.0 standard)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_JointPhotographicExpertsGroup: Coding = {
  code: 'JPEG',
  display: 'Joint Photographic Experts Group',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_UninterpretedBinaryData: Coding = {
  code: 'Octet-stream',
  display: 'Uninterpreted binary data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_PICTFormatImageData: Coding = {
  code: 'PICT',
  display: 'PICT format image data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_PostScriptProgram: Coding = {
  code: 'PostScript',
  display: 'PostScript program',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_RichTextFormat: Coding = {
  code: 'RTF',
  display: 'Rich Text Format',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_StandardGeneralizedMarkupLanguageHL7V231AndLater: Coding = {
  code: 'SGML',
  display: 'Standard Generalized Markup Language (HL7 V2.3.1 and later)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_TIFFImageData: Coding = {
  code: 'TIFF',
  display: 'TIFF image data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_HL7ClinicalDocumentArchitectureLevelOneDocument: Coding = {
  code: 'x-hl7-cda-level-one',
  display: 'HL7 Clinical Document Architecture Level One document',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
const v2_0291_ExtensibleMarkupLanguageHL7V231AndLater: Coding = {
  code: 'XML',
  display: 'Extensible Markup Language (HL7 V2.3.1 and later)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0291',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0291 ( Subtype of Referenced Data)
 */
export const V20291 = {
  ISDNPCMAudioData: v2_0291_ISDNPCMAudioData,
  DigitalImagingAndCommunicationsInMedicine: v2_0291_DigitalImagingAndCommunicationsInMedicine,
  FacsimileData: v2_0291_FacsimileData,
  GraphicsInterchangeFormat: v2_0291_GraphicsInterchangeFormat,
  HypertextMarkupLanguage: v2_0291_HypertextMarkupLanguage,
  ElectronicInkDataJot10Standard: v2_0291_ElectronicInkDataJot10Standard,
  JointPhotographicExpertsGroup: v2_0291_JointPhotographicExpertsGroup,
  UninterpretedBinaryData: v2_0291_UninterpretedBinaryData,
  PICTFormatImageData: v2_0291_PICTFormatImageData,
  PostScriptProgram: v2_0291_PostScriptProgram,
  RichTextFormat: v2_0291_RichTextFormat,
  StandardGeneralizedMarkupLanguageHL7V231AndLater: v2_0291_StandardGeneralizedMarkupLanguageHL7V231AndLater,
  TIFFImageData: v2_0291_TIFFImageData,
  HL7ClinicalDocumentArchitectureLevelOneDocument: v2_0291_HL7ClinicalDocumentArchitectureLevelOneDocument,
  ExtensibleMarkupLanguageHL7V231AndLater: v2_0291_ExtensibleMarkupLanguageHL7V231AndLater,
};
const v2_0294_AnIndicatorThatFridayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: Coding = {
  code: 'Fri',
  display: 'An indicator that Friday is or is not preferred for the day on which the appointment will occur.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0294',
};
const v2_0294_AnIndicatorThatMondayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: Coding = {
  code: 'Mon',
  display: 'An indicator that Monday is or is not preferred for the day on which the appointment will occur.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0294',
};
const v2_0294_AnIndicatorThatThereIsAPreferredEndTimeForTheAppointmentRequestServiceOrResource: Coding = {
  code: 'Prefend',
  display: 'An indicator that there is a preferred end time for the appointment request, service or resource.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0294',
};
const v2_0294_AnIndicatorThatThereIsAPreferredStartTimeForTheAppointmentRequestServiceOrResource: Coding = {
  code: 'Prefstart',
  display: 'An indicator that there is a preferred start time for the appointment request, service or resource.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0294',
};
const v2_0294_AnIndicatorThatSaturdayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: Coding = {
  code: 'Sat',
  display: 'An indicator that Saturday is or is not preferred for the day on which the appointment will occur.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0294',
};
const v2_0294_AnIndicatorThatSundayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: Coding = {
  code: 'Sun',
  display: 'An indicator that Sunday is or is not preferred for the day on which the appointment will occur.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0294',
};
const v2_0294_AnIndicatorThatThursdayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: Coding = {
  code: 'Thu',
  display: 'An indicator that Thursday is or is not preferred for the day on which the appointment will occur.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0294',
};
const v2_0294_AnIndicatorThatTuesdayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: Coding = {
  code: 'Tue',
  display: 'An indicator that Tuesday is or is not preferred for the day on which the appointment will occur.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0294',
};
const v2_0294_AnIndicatorThatWednesdayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: Coding = {
  code: 'Wed',
  display: 'An indicator that Wednesday is or is not preferred for the day on which the appointment will occur.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0294',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0294 ( Time Selection Criteria Parameter Class Codes)
 */
export const V20294 = {
  AnIndicatorThatFridayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: v2_0294_AnIndicatorThatFridayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur,
  AnIndicatorThatMondayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: v2_0294_AnIndicatorThatMondayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur,
  AnIndicatorThatThereIsAPreferredEndTimeForTheAppointmentRequestServiceOrResource: v2_0294_AnIndicatorThatThereIsAPreferredEndTimeForTheAppointmentRequestServiceOrResource,
  AnIndicatorThatThereIsAPreferredStartTimeForTheAppointmentRequestServiceOrResource: v2_0294_AnIndicatorThatThereIsAPreferredStartTimeForTheAppointmentRequestServiceOrResource,
  AnIndicatorThatSaturdayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: v2_0294_AnIndicatorThatSaturdayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur,
  AnIndicatorThatSundayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: v2_0294_AnIndicatorThatSundayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur,
  AnIndicatorThatThursdayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: v2_0294_AnIndicatorThatThursdayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur,
  AnIndicatorThatTuesdayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: v2_0294_AnIndicatorThatTuesdayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur,
  AnIndicatorThatWednesdayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur: v2_0294_AnIndicatorThatWednesdayIsOrIsNotPreferredForTheDayOnWhichTheAppointmentWillOccur,
};
const v2_0298_FlatRateApplyTheEntirePriceToThisIntervalDoNotProRateThePriceIfTheFullIntervalHasNotOccurredBeenConsumed: Coding = {
  code: 'F',
  display:
    'Flat-rate. Apply the entire price to this interval, do not pro-rate the price if the full interval has not occurred/been consumed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0298',
};
const v2_0298_ProRateApplyThisPriceToThisIntervalProRatedByWhateverPortionOfTheIntervalHasOccurredBeenConsumed: Coding = {
  code: 'P',
  display: 'Pro-rate. Apply this price to this interval, pro-rated by whatever portion of the interval has occurred/been consumed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0298',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0298 ( CP Range Type)
 */
export const V20298 = {
  FlatRateApplyTheEntirePriceToThisIntervalDoNotProRateThePriceIfTheFullIntervalHasNotOccurredBeenConsumed: v2_0298_FlatRateApplyTheEntirePriceToThisIntervalDoNotProRateThePriceIfTheFullIntervalHasNotOccurredBeenConsumed,
  ProRateApplyThisPriceToThisIntervalProRatedByWhateverPortionOfTheIntervalHasOccurredBeenConsumed: v2_0298_ProRateApplyThisPriceToThisIntervalProRatedByWhateverPortionOfTheIntervalHasOccurredBeenConsumed,
};
const v2_0299_NoEncodingDataAreDisplayableASCIICharacters: Coding = {
  code: 'A',
  display: 'No encoding - data are displayable ASCII characters.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0299',
};
const v2_0299_EncodingAsDefinedByMIMEMultipurposeInternetMailExtensionsStandardRFC1521FourConsecutiveASCIICharactersRepresentThreeConsecutiveOctetsOfBinaryDataBase64UtilizesA65CharacterSubsetOfUSASCIIConsistingOfBothTheUpperAnd: Coding = {
  code: 'Base64',
  display:
    'Encoding as defined by MIME (Multipurpose Internet Mail Extensions) standard RFC 1521. Four consecutive ASCII characters represent three consecutive octets of binary data. Base64 utilizes a 65-character subset of US-ASCII, consisting of both the upper and',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0299',
};
const v2_0299_HexadecimalEncodingConsecutivePairsOfHexadecimalDigitsRepresentConsecutiveSingleOctets: Coding = {
  code: 'Hex',
  display: 'Hexadecimal encoding - consecutive pairs of hexadecimal digits represent consecutive single octets.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0299',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0299 ( Encoding)
 */
export const V20299 = {
  NoEncodingDataAreDisplayableASCIICharacters: v2_0299_NoEncodingDataAreDisplayableASCIICharacters,
  EncodingAsDefinedByMIMEMultipurposeInternetMailExtensionsStandardRFC1521FourConsecutiveASCIICharactersRepresentThreeConsecutiveOctetsOfBinaryDataBase64UtilizesA65CharacterSubsetOfUSASCIIConsistingOfBothTheUpperAnd: v2_0299_EncodingAsDefinedByMIMEMultipurposeInternetMailExtensionsStandardRFC1521FourConsecutiveASCIICharactersRepresentThreeConsecutiveOctetsOfBinaryDataBase64UtilizesA65CharacterSubsetOfUSASCIIConsistingOfBothTheUpperAnd,
  HexadecimalEncodingConsecutivePairsOfHexadecimalDigitsRepresentConsecutiveSingleOctets: v2_0299_HexadecimalEncodingConsecutivePairsOfHexadecimalDigitsRepresentConsecutiveSingleOctets,
};
const v2_0301_CollegeOfAmericanPathologistAccreditationNumber: Coding = {
  code: 'CAP',
  display: 'College of American Pathologist Accreditation Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_ClinicalLaboratoryImprovementAmendments: Coding = {
  code: 'CLIA',
  display: 'Clinical Laboratory Improvement Amendments',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_ClinicalLaboratoryImprovementProgram: Coding = {
  code: 'CLIP',
  display: 'Clinical laboratory Improvement Program',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_DomainNameSystem: Coding = {
  code: 'DNS',
  display: 'Domain Name System',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_IEEE64BitExtendedUniqueIdentifier: Coding = {
  code: 'EUI64',
  display: 'IEEE 64-bit Extended Unique Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_GloballyUniqueIdentifier: Coding = {
  code: 'GUID',
  display: 'globally unique identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_CENHealthcareCodingIdentifier: Coding = {
  code: 'HCD',
  display: 'CEN Healthcare Coding Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_HL7RegistrationSchemes: Coding = {
  code: 'HL7',
  display: 'HL7 registration schemes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_ISOObjectIdentifier: Coding = {
  code: 'ISO',
  display: 'ISO Object Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_Local: Coding = {
  code: 'L',
  display: 'Local',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_Local_2: Coding = {
  code: 'L,M,N',
  display: 'Local',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_Local_3: Coding = {
  code: 'M',
  display: 'Local',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_Local_4: Coding = {
  code: 'N',
  display: 'Local',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_USNationalProviderIdentifier: Coding = {
  code: 'NPI',
  display: 'US National Provider Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_Random: Coding = {
  code: 'Random',
  display: 'Random',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_UniformResourceIdentifier: Coding = {
  code: 'URI',
  display: 'Uniform Resource Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_UniversalUniqueIdentifier: Coding = {
  code: 'UUID',
  display: 'Universal Unique Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_X400MHSIdentifier: Coding = {
  code: 'x400',
  display: 'X.400 MHS identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
const v2_0301_X500DirectoryName: Coding = {
  code: 'x500',
  display: 'X500 directory Name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0301',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0301 ( Universal ID type)
 */
export const V20301 = {
  CollegeOfAmericanPathologistAccreditationNumber: v2_0301_CollegeOfAmericanPathologistAccreditationNumber,
  ClinicalLaboratoryImprovementAmendments: v2_0301_ClinicalLaboratoryImprovementAmendments,
  ClinicalLaboratoryImprovementProgram: v2_0301_ClinicalLaboratoryImprovementProgram,
  DomainNameSystem: v2_0301_DomainNameSystem,
  IEEE64BitExtendedUniqueIdentifier: v2_0301_IEEE64BitExtendedUniqueIdentifier,
  GloballyUniqueIdentifier: v2_0301_GloballyUniqueIdentifier,
  CENHealthcareCodingIdentifier: v2_0301_CENHealthcareCodingIdentifier,
  HL7RegistrationSchemes: v2_0301_HL7RegistrationSchemes,
  ISOObjectIdentifier: v2_0301_ISOObjectIdentifier,
  Local: v2_0301_Local,
  Local_2: v2_0301_Local_2,
  Local_3: v2_0301_Local_3,
  Local_4: v2_0301_Local_4,
  USNationalProviderIdentifier: v2_0301_USNationalProviderIdentifier,
  Random: v2_0301_Random,
  UniformResourceIdentifier: v2_0301_UniformResourceIdentifier,
  UniversalUniqueIdentifier: v2_0301_UniversalUniqueIdentifier,
  X400MHSIdentifier: v2_0301_X400MHSIdentifier,
  X500DirectoryName: v2_0301_X500DirectoryName,
};
const v2_0305_Clinic: Coding = {
  code: 'C',
  display: 'Clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0305',
};
const v2_0305_Department: Coding = {
  code: 'D',
  display: 'Department',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0305',
};
const v2_0305_Home: Coding = {
  code: 'H',
  display: 'Home',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0305',
};
const v2_0305_NursingUnit: Coding = {
  code: 'N',
  display: 'Nursing Unit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0305',
};
const v2_0305_ProviderQuoteSOffice: Coding = {
  code: 'O',
  display: "Provider's Office",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0305',
};
const v2_0305_Phone: Coding = {
  code: 'P',
  display: 'Phone',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0305',
};
const v2_0305_SNF: Coding = {
  code: 'S',
  display: 'SNF',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0305',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0305 ( Person location type)
 */
export const V20305 = {
  Clinic: v2_0305_Clinic,
  Department: v2_0305_Department,
  Home: v2_0305_Home,
  NursingUnit: v2_0305_NursingUnit,
  ProviderQuoteSOffice: v2_0305_ProviderQuoteSOffice,
  Phone: v2_0305_Phone,
  SNF: v2_0305_SNF,
};
const v2_0309_BothHospitalAndPhysician: Coding = {
  code: 'B',
  display: 'Both hospital and physician',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0309',
};
const v2_0309_HospitalInstitutional: Coding = {
  code: 'H',
  display: 'Hospital/institutional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0309',
};
const v2_0309_PhysicianProfessional: Coding = {
  code: 'P',
  display: 'Physician/professional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0309',
};
const v2_0309_Pharmacy: Coding = {
  code: 'RX',
  display: 'Pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0309',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0309 ( Coverage Type)
 */
export const V20309 = {
  BothHospitalAndPhysician: v2_0309_BothHospitalAndPhysician,
  HospitalInstitutional: v2_0309_HospitalInstitutional,
  PhysicianProfessional: v2_0309_PhysicianProfessional,
  Pharmacy: v2_0309_Pharmacy,
};
const v2_0311_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0311',
};
const v2_0311_Permanent: Coding = {
  code: 'P',
  display: 'Permanent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0311',
};
const v2_0311_Temporary: Coding = {
  code: 'T',
  display: 'Temporary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0311',
};
const v2_0311_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0311',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0311 ( Job status)
 */
export const V20311 = {
  Other: v2_0311_Other,
  Permanent: v2_0311_Permanent,
  Temporary: v2_0311_Temporary,
  Unknown: v2_0311_Unknown,
};
const v2_0315_YesPatientHasALivingWillButItIsNotOnFile: Coding = {
  code: 'F',
  display: 'Yes, patient has a living will but it is not on file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0315',
};
const v2_0315_NoPatientDoesNotHaveALivingWillButInformationWasProvided: Coding = {
  code: 'I',
  display: 'No, patient does not have a living will but information was provided',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0315',
};
const v2_0315_NoPatientDoesNotHaveALivingWillAndNoInformationWasProvided: Coding = {
  code: 'N',
  display: 'No, patient does not have a living will and no information was provided',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0315',
};
const v2_0315_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0315',
};
const v2_0315_YesPatientHasALivingWill: Coding = {
  code: 'Y',
  display: 'Yes, patient has a living will',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0315',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0315 ( Living Will)
 */
export const V20315 = {
  YesPatientHasALivingWillButItIsNotOnFile: v2_0315_YesPatientHasALivingWillButItIsNotOnFile,
  NoPatientDoesNotHaveALivingWillButInformationWasProvided: v2_0315_NoPatientDoesNotHaveALivingWillButInformationWasProvided,
  NoPatientDoesNotHaveALivingWillAndNoInformationWasProvided: v2_0315_NoPatientDoesNotHaveALivingWillAndNoInformationWasProvided,
  Unknown: v2_0315_Unknown,
  YesPatientHasALivingWill: v2_0315_YesPatientHasALivingWill,
};
const v2_0316_YesPatientIsADocumentedDonorButDocumentationIsNotOnFile: Coding = {
  code: 'F',
  display: 'Yes, patient is a documented donor, but documentation is not on file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0316',
};
const v2_0316_NoPatientIsNotADocumentedDonorButInformationWasProvided: Coding = {
  code: 'I',
  display: 'No, patient is not a documented donor, but information was provided',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0316',
};
const v2_0316_NoPatientHasNotAgreedToBeADonor: Coding = {
  code: 'N',
  display: 'No, patient has not agreed to be a donor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0316',
};
const v2_0316_PatientLeavesOrganDonationDecisionToASpecificPerson: Coding = {
  code: 'P',
  display: 'Patient leaves organ donation decision to a specific person',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0316',
};
const v2_0316_PatientLeavesOrganDonationDecisionToRelatives: Coding = {
  code: 'R',
  display: 'Patient leaves organ donation decision to relatives',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0316',
};
const v2_0316_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0316',
};
const v2_0316_YesPatientIsADocumentedDonorAndDocumentationIsOnFile: Coding = {
  code: 'Y',
  display: 'Yes, patient is a documented donor and documentation is on file',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0316',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0316 ( Organ Donor)
 */
export const V20316 = {
  YesPatientIsADocumentedDonorButDocumentationIsNotOnFile: v2_0316_YesPatientIsADocumentedDonorButDocumentationIsNotOnFile,
  NoPatientIsNotADocumentedDonorButInformationWasProvided: v2_0316_NoPatientIsNotADocumentedDonorButInformationWasProvided,
  NoPatientHasNotAgreedToBeADonor: v2_0316_NoPatientHasNotAgreedToBeADonor,
  PatientLeavesOrganDonationDecisionToASpecificPerson: v2_0316_PatientLeavesOrganDonationDecisionToASpecificPerson,
  PatientLeavesOrganDonationDecisionToRelatives: v2_0316_PatientLeavesOrganDonationDecisionToRelatives,
  Unknown: v2_0316_Unknown,
  YesPatientIsADocumentedDonorAndDocumentationIsOnFile: v2_0316_YesPatientIsADocumentedDonorAndDocumentationIsOnFile,
};
const v2_0317_PaceSpike: Coding = {
  code: '9900',
  display: 'Pace spike',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0317',
};
const v2_0317_SASMarker: Coding = {
  code: '9901',
  display: 'SAS marker',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0317',
};
const v2_0317_SenseMarker: Coding = {
  code: '9902',
  display: 'Sense marker',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0317',
};
const v2_0317_BeatMarker: Coding = {
  code: '9903',
  display: 'Beat marker',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0317',
};
const v2_0317_Etc: Coding = {
  code: '9904',
  display: 'etc.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0317',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0317 ( Annotations)
 */
export const V20317 = {
  PaceSpike: v2_0317_PaceSpike,
  SASMarker: v2_0317_SASMarker,
  SenseMarker: v2_0317_SenseMarker,
  BeatMarker: v2_0317_BeatMarker,
  Etc: v2_0317_Etc,
};
const v2_0321_AutomaticDispensing: Coding = {
  code: 'AD',
  display: 'Automatic Dispensing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0321',
};
const v2_0321_FloorStock: Coding = {
  code: 'F',
  display: 'Floor Stock',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0321',
};
const v2_0321_Traditional: Coding = {
  code: 'TR',
  display: 'Traditional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0321',
};
const v2_0321_UnitDose: Coding = {
  code: 'UD',
  display: 'Unit Dose',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0321',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0321 ( Dispense Method)
 */
export const V20321 = {
  AutomaticDispensing: v2_0321_AutomaticDispensing,
  FloorStock: v2_0321_FloorStock,
  Traditional: v2_0321_Traditional,
  UnitDose: v2_0321_UnitDose,
};
const v2_0322_Complete: Coding = {
  code: 'CP',
  display: 'Complete',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0322',
};
const v2_0322_NotAdministered: Coding = {
  code: 'NA',
  display: 'Not Administered',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0322',
};
const v2_0322_PartiallyAdministered: Coding = {
  code: 'PA',
  display: 'Partially Administered',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0322',
};
const v2_0322_Refused: Coding = {
  code: 'RE',
  display: 'Refused',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0322',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0322 ( Completion Status)
 */
export const V20322 = {
  Complete: v2_0322_Complete,
  NotAdministered: v2_0322_NotAdministered,
  PartiallyAdministered: v2_0322_PartiallyAdministered,
  Refused: v2_0322_Refused,
};
const v2_0324_GenderOfPatientS: Coding = {
  code: 'GEN',
  display: 'Gender of patient(s)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0324',
};
const v2_0324_ImplantCanBeUsedForRadiationImplantPatients: Coding = {
  code: 'IMP',
  display: 'Implant: can be used for radiation implant patients',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0324',
};
const v2_0324_InfectiousDiseaseThisLocationCanBeUsedForIsolation: Coding = {
  code: 'INF',
  display: 'Infectious disease: this location can be used for isolation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0324',
};
const v2_0324_LevelOfCare: Coding = {
  code: 'LCR',
  display: 'Level of care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0324',
};
const v2_0324_Licensed: Coding = {
  code: 'LIC',
  display: 'Licensed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0324',
};
const v2_0324_Overflow: Coding = {
  code: 'OVR',
  display: 'Overflow',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0324',
};
const v2_0324_PrivacyLevelIndicatingTheLevelOfPrivateVersusNonPrivateRoom: Coding = {
  code: 'PRL',
  display: 'Privacy level: indicating the level of private versus non-private room',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0324',
};
const v2_0324_BedIsSetUp: Coding = {
  code: 'SET',
  display: 'Bed is set up',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0324',
};
const v2_0324_ShadowATemporaryHoldingLocationThatDoesNotPhysicallyExist: Coding = {
  code: 'SHA',
  display: 'Shadow: a temporary holding location that does not physically exist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0324',
};
const v2_0324_Smoking: Coding = {
  code: 'SMK',
  display: 'Smoking',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0324',
};
const v2_0324_BedIsStaffed: Coding = {
  code: 'STF',
  display: 'Bed is staffed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0324',
};
const v2_0324_TeachingLocation: Coding = {
  code: 'TEA',
  display: 'Teaching location',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0324',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0324 ( Location Characteristic ID)
 */
export const V20324 = {
  GenderOfPatientS: v2_0324_GenderOfPatientS,
  ImplantCanBeUsedForRadiationImplantPatients: v2_0324_ImplantCanBeUsedForRadiationImplantPatients,
  InfectiousDiseaseThisLocationCanBeUsedForIsolation: v2_0324_InfectiousDiseaseThisLocationCanBeUsedForIsolation,
  LevelOfCare: v2_0324_LevelOfCare,
  Licensed: v2_0324_Licensed,
  Overflow: v2_0324_Overflow,
  PrivacyLevelIndicatingTheLevelOfPrivateVersusNonPrivateRoom: v2_0324_PrivacyLevelIndicatingTheLevelOfPrivateVersusNonPrivateRoom,
  BedIsSetUp: v2_0324_BedIsSetUp,
  ShadowATemporaryHoldingLocationThatDoesNotPhysicallyExist: v2_0324_ShadowATemporaryHoldingLocationThatDoesNotPhysicallyExist,
  Smoking: v2_0324_Smoking,
  BedIsStaffed: v2_0324_BedIsStaffed,
  TeachingLocation: v2_0324_TeachingLocation,
};
const v2_0325_LocationAliasEs: Coding = {
  code: 'ALI',
  display: 'Location Alias(es)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0325',
};
const v2_0325_NearestDietaryLocation: Coding = {
  code: 'DTY',
  display: 'Nearest  dietary location',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0325',
};
const v2_0325_NearestLab: Coding = {
  code: 'LAB',
  display: 'Nearest  lab',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0325',
};
const v2_0325_SecondNearestLab: Coding = {
  code: 'LB2',
  display: 'Second nearest lab',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0325',
};
const v2_0325_ParentLocation: Coding = {
  code: 'PAR',
  display: 'Parent location',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0325',
};
const v2_0325_NearestPharmacy: Coding = {
  code: 'RX',
  display: 'Nearest  pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0325',
};
const v2_0325_SecondNearestPharmacy: Coding = {
  code: 'RX2',
  display: 'Second nearest pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0325',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0325 ( Location Relationship ID)
 */
export const V20325 = {
  LocationAliasEs: v2_0325_LocationAliasEs,
  NearestDietaryLocation: v2_0325_NearestDietaryLocation,
  NearestLab: v2_0325_NearestLab,
  SecondNearestLab: v2_0325_SecondNearestLab,
  ParentLocation: v2_0325_ParentLocation,
  NearestPharmacy: v2_0325_NearestPharmacy,
  SecondNearestPharmacy: v2_0325_SecondNearestPharmacy,
};
const v2_0326_AccountLevel: Coding = {
  code: 'A',
  display: 'Account level',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0326',
};
const v2_0326_VisitLevel: Coding = {
  code: 'V',
  display: 'Visit level',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0326',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0326 ( Visit Indicator)
 */
export const V20326 = {
  AccountLevel: v2_0326_AccountLevel,
  VisitLevel: v2_0326_VisitLevel,
};
const v2_0329_ActualCount: Coding = {
  code: 'A',
  display: 'Actual count',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0329',
};
const v2_0329_EstimatedSeeComment: Coding = {
  code: 'E',
  display: 'Estimated (see comment)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0329',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0329 ( Quantity Method)
 */
export const V20329 = {
  ActualCount: v2_0329_ActualCount,
  EstimatedSeeComment: v2_0329_EstimatedSeeComment,
};
const v2_0330_VAL510KExempt: Coding = {
  code: '510E',
  display: '510 (K) exempt',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0330',
};
const v2_0330_VAL510K: Coding = {
  code: '510K',
  display: '510 (K)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0330',
};
const v2_0330_PostMarketingStudy522: Coding = {
  code: '522S',
  display: 'Post marketing study (522)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0330',
};
const v2_0330_PremarketingAuthorization: Coding = {
  code: 'PMA',
  display: 'Premarketing authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0330',
};
const v2_0330_Preamendment: Coding = {
  code: 'PRE',
  display: 'Preamendment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0330',
};
const v2_0330_Transitional: Coding = {
  code: 'TXN',
  display: 'Transitional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0330',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0330 ( Marketing Basis)
 */
export const V20330 = {
  VAL510KExempt: v2_0330_VAL510KExempt,
  VAL510K: v2_0330_VAL510K,
  PostMarketingStudy522: v2_0330_PostMarketingStudy522,
  PremarketingAuthorization: v2_0330_PremarketingAuthorization,
  Preamendment: v2_0330_Preamendment,
  Transitional: v2_0330_Transitional,
};
const v2_0331_AgentForAForeignManufacturer: Coding = {
  code: 'A',
  display: 'Agent for a foreign manufacturer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0331',
};
const v2_0331_Distributor: Coding = {
  code: 'D',
  display: 'Distributor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0331',
};
const v2_0331_Manufacturer: Coding = {
  code: 'M',
  display: 'Manufacturer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0331',
};
const v2_0331_User: Coding = {
  code: 'U',
  display: 'User',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0331',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0331 ( Facility Type)
 */
export const V20331 = {
  AgentForAForeignManufacturer: v2_0331_AgentForAForeignManufacturer,
  Distributor: v2_0331_Distributor,
  Manufacturer: v2_0331_Manufacturer,
  User: v2_0331_User,
};
const v2_0332_Accept: Coding = {
  code: 'A',
  display: 'Accept',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0332',
};
const v2_0332_Initiate: Coding = {
  code: 'I',
  display: 'Initiate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0332',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0332 ( Network Source Type)
 */
export const V20332 = {
  Accept: v2_0332_Accept,
  Initiate: v2_0332_Initiate,
};
const v2_0334_AssociatedParty: Coding = {
  code: 'AP',
  display: 'Associated party',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0334',
};
const v2_0334_Guarantor: Coding = {
  code: 'GT',
  display: 'Guarantor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0334',
};
const v2_0334_Insured: Coding = {
  code: 'IN',
  display: 'Insured',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0334',
};
const v2_0334_Patient: Coding = {
  code: 'PT',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0334',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0334 ( Disabled Person)
 */
export const V20334 = {
  AssociatedParty: v2_0334_AssociatedParty,
  Guarantor: v2_0334_Guarantor,
  Insured: v2_0334_Insured,
  Patient: v2_0334_Patient,
};
const v2_0335_AnteBefore: Coding = {
  code: 'A',
  display: 'Ante (before)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_TwiceADayAtInstitutionSpecifiedTimes: Coding = {
  code: 'BID',
  display: 'twice a day at institution-specified times',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_ServiceIsProvidedContinuouslyBetweenStartTimeAndStopTime: Coding = {
  code: 'C',
  display: 'service is provided continuously between start time and stop time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_CibusDiurnusLunch: Coding = {
  code: 'D',
  display: 'Cibus Diurnus (lunch)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_Inter: Coding = {
  code: 'I',
  display: 'Inter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_CibusMatutinusBreakfast: Coding = {
  code: 'M',
  display: 'Cibus Matutinus (breakfast)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_LessThanTimingGreaterThanCQuotationCumQuotationLessThanMealGreaterThan: Coding = {
  code: 'Meal Related Timings',
  display: '<timing>C ("cum")<meal>',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_OneTimeOnly: Coding = {
  code: 'Once',
  display: 'one time only.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_PostAfter: Coding = {
  code: 'P',
  display: 'Post (after)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_GivenAsNeeded: Coding = {
  code: 'PRN',
  display: 'given as needed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_WhereXxxIsSomeFrequencyCode: Coding = {
  code: 'PRNxxx',
  display: 'where xxx is some frequency code',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_EveryLessThanIntegerGreaterThanDays: Coding = {
  code: 'Q<integer>D',
  display: 'every <integer> days',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_EveryLessThanIntegerGreaterThanHours: Coding = {
  code: 'Q<integer>H',
  display: 'every <integer> hours',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_RepeatsOnAParticularDayOfTheWeek: Coding = {
  code: 'Q<integer>J<day#>',
  display: 'repeats on a particular day of the week,',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_EveryLessThanIntegerGreaterThanMonthsLunarCycle: Coding = {
  code: 'Q<integer>L',
  display: 'every <integer> months (Lunar cycle)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_EveryLessThanIntegerGreaterThanMinutes: Coding = {
  code: 'Q<integer>M',
  display: 'every <integer> minutes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_EveryLessThanIntegerGreaterThanSeconds: Coding = {
  code: 'Q<integer>S',
  display: 'every <integer> seconds',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_EveryLessThanIntegerGreaterThanWeeks: Coding = {
  code: 'Q<integer>W',
  display: 'every <integer> weeks',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_InTheMorningAtInstitutionSpecifiedTime: Coding = {
  code: 'QAM',
  display: 'in the morning at institution-specified time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_EveryDayBeforeTheHourOfSleep: Coding = {
  code: 'QHS',
  display: 'every day before the hour of sleep',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_FourTimesADayAtInstitutionSpecifiedTimes: Coding = {
  code: 'QID',
  display: 'four times a day at institution-specified times',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_EveryOtherDay: Coding = {
  code: 'QOD',
  display: 'every other day',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_InTheEveningAtInstitutionSpecifiedTime: Coding = {
  code: 'QPM',
  display: 'in the evening at institution-specified time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_DuringEachOfThreeEightHourShiftsAtInstitutionSpecifiedTimes: Coding = {
  code: 'QSHIFT',
  display: 'during each of three eight-hour shifts at institution-specified times',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_ThreeTimesADayAtInstitutionSpecifiedTimes: Coding = {
  code: 'TID',
  display: 'three times a day at institution-specified times',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_ForFutureUseWhereLessThanSpecGreaterThanIsAnIntervalSpecificationAsDefinedByTheUNIXCronSpecification: Coding = {
  code: 'U <spec>',
  display: 'for future use, where <spec> is an interval specification as defined by the UNIX cron specification.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_CibusVespertinusDinner: Coding = {
  code: 'V',
  display: 'Cibus Vespertinus (dinner)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
const v2_0335_QuotationXQuotationTimesPerDayAtInstitutionSpecifiedTimesWhereXIsANumeral5OrGreater: Coding = {
  code: 'xID',
  display: '"X" times per day at institution-specified times, where X is a numeral 5 or greater.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0335',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0335 ( Repeat pattern)
 */
export const V20335 = {
  AnteBefore: v2_0335_AnteBefore,
  TwiceADayAtInstitutionSpecifiedTimes: v2_0335_TwiceADayAtInstitutionSpecifiedTimes,
  ServiceIsProvidedContinuouslyBetweenStartTimeAndStopTime: v2_0335_ServiceIsProvidedContinuouslyBetweenStartTimeAndStopTime,
  CibusDiurnusLunch: v2_0335_CibusDiurnusLunch,
  Inter: v2_0335_Inter,
  CibusMatutinusBreakfast: v2_0335_CibusMatutinusBreakfast,
  LessThanTimingGreaterThanCQuotationCumQuotationLessThanMealGreaterThan: v2_0335_LessThanTimingGreaterThanCQuotationCumQuotationLessThanMealGreaterThan,
  OneTimeOnly: v2_0335_OneTimeOnly,
  PostAfter: v2_0335_PostAfter,
  GivenAsNeeded: v2_0335_GivenAsNeeded,
  WhereXxxIsSomeFrequencyCode: v2_0335_WhereXxxIsSomeFrequencyCode,
  EveryLessThanIntegerGreaterThanDays: v2_0335_EveryLessThanIntegerGreaterThanDays,
  EveryLessThanIntegerGreaterThanHours: v2_0335_EveryLessThanIntegerGreaterThanHours,
  RepeatsOnAParticularDayOfTheWeek: v2_0335_RepeatsOnAParticularDayOfTheWeek,
  EveryLessThanIntegerGreaterThanMonthsLunarCycle: v2_0335_EveryLessThanIntegerGreaterThanMonthsLunarCycle,
  EveryLessThanIntegerGreaterThanMinutes: v2_0335_EveryLessThanIntegerGreaterThanMinutes,
  EveryLessThanIntegerGreaterThanSeconds: v2_0335_EveryLessThanIntegerGreaterThanSeconds,
  EveryLessThanIntegerGreaterThanWeeks: v2_0335_EveryLessThanIntegerGreaterThanWeeks,
  InTheMorningAtInstitutionSpecifiedTime: v2_0335_InTheMorningAtInstitutionSpecifiedTime,
  EveryDayBeforeTheHourOfSleep: v2_0335_EveryDayBeforeTheHourOfSleep,
  FourTimesADayAtInstitutionSpecifiedTimes: v2_0335_FourTimesADayAtInstitutionSpecifiedTimes,
  EveryOtherDay: v2_0335_EveryOtherDay,
  InTheEveningAtInstitutionSpecifiedTime: v2_0335_InTheEveningAtInstitutionSpecifiedTime,
  DuringEachOfThreeEightHourShiftsAtInstitutionSpecifiedTimes: v2_0335_DuringEachOfThreeEightHourShiftsAtInstitutionSpecifiedTimes,
  ThreeTimesADayAtInstitutionSpecifiedTimes: v2_0335_ThreeTimesADayAtInstitutionSpecifiedTimes,
  ForFutureUseWhereLessThanSpecGreaterThanIsAnIntervalSpecificationAsDefinedByTheUNIXCronSpecification: v2_0335_ForFutureUseWhereLessThanSpecGreaterThanIsAnIntervalSpecificationAsDefinedByTheUNIXCronSpecification,
  CibusVespertinusDinner: v2_0335_CibusVespertinusDinner,
  QuotationXQuotationTimesPerDayAtInstitutionSpecifiedTimesWhereXIsANumeral5OrGreater: v2_0335_QuotationXQuotationTimesPerDayAtInstitutionSpecifiedTimesWhereXIsANumeral5OrGreater,
};
const v2_0336_ProviderOrdered: Coding = {
  code: 'O',
  display: 'Provider Ordered',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0336',
};
const v2_0336_PatientPreference: Coding = {
  code: 'P',
  display: 'Patient Preference',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0336',
};
const v2_0336_SecondOpinion: Coding = {
  code: 'S',
  display: 'Second Opinion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0336',
};
const v2_0336_WorkLoad: Coding = {
  code: 'W',
  display: 'Work Load',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0336',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0336 ( Referral Reason)
 */
export const V20336 = {
  ProviderOrdered: v2_0336_ProviderOrdered,
  PatientPreference: v2_0336_PatientPreference,
  SecondOpinion: v2_0336_SecondOpinion,
  WorkLoad: v2_0336_WorkLoad,
};
const v2_0337_Certified: Coding = {
  code: 'C',
  display: 'Certified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0337',
};
const v2_0337_Eligible: Coding = {
  code: 'E',
  display: 'Eligible',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0337',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0337 ( Certification Status)
 */
export const V20337 = {
  Certified: v2_0337_Certified,
  Eligible: v2_0337_Eligible,
};
const v2_0338_CountyNumber: Coding = {
  code: 'CY',
  display: 'County number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0338',
};
const v2_0338_DrugEnforcementAgencyNo: Coding = {
  code: 'DEA',
  display: 'Drug Enforcement Agency no.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0338',
};
const v2_0338_GeneralLedgerNumber: Coding = {
  code: 'GL',
  display: 'General ledger number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0338',
};
const v2_0338_LaborAndIndustriesNumber: Coding = {
  code: 'L&I',
  display: 'Labor and industries number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0338',
};
const v2_0338_LaborAndIndustriesNumber_2: Coding = {
  code: 'LI',
  display: 'Labor and industries number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0338',
};
const v2_0338_MedicaidNumber: Coding = {
  code: 'MCD',
  display: 'Medicaid number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0338',
};
const v2_0338_MedicareNumber: Coding = {
  code: 'MCR',
  display: 'Medicare number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0338',
};
const v2_0338_QANumber: Coding = {
  code: 'QA',
  display: 'QA number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0338',
};
const v2_0338_StateLicenseNumber: Coding = {
  code: 'SL',
  display: 'State license number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0338',
};
const v2_0338_TaxIDNumber: Coding = {
  code: 'TAX',
  display: 'Tax ID number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0338',
};
const v2_0338_TrainingLicenseNumber: Coding = {
  code: 'TRL',
  display: 'Training license number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0338',
};
const v2_0338_UniquePhysicianIDNo: Coding = {
  code: 'UPIN',
  display: 'Unique physician ID no.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0338',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0338 ( Practitioner ID number type)
 */
export const V20338 = {
  CountyNumber: v2_0338_CountyNumber,
  DrugEnforcementAgencyNo: v2_0338_DrugEnforcementAgencyNo,
  GeneralLedgerNumber: v2_0338_GeneralLedgerNumber,
  LaborAndIndustriesNumber: v2_0338_LaborAndIndustriesNumber,
  LaborAndIndustriesNumber_2: v2_0338_LaborAndIndustriesNumber_2,
  MedicaidNumber: v2_0338_MedicaidNumber,
  MedicareNumber: v2_0338_MedicareNumber,
  QANumber: v2_0338_QANumber,
  StateLicenseNumber: v2_0338_StateLicenseNumber,
  TaxIDNumber: v2_0338_TaxIDNumber,
  TrainingLicenseNumber: v2_0338_TrainingLicenseNumber,
  UniquePhysicianIDNo: v2_0338_UniquePhysicianIDNo,
};
const v2_0339_ServiceIsSubjectToMedicalNecessityProcedures: Coding = {
  code: '1',
  display: 'Service is subject to medical necessity procedures',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0339',
};
const v2_0339_PatientHasBeenInformedOfResponsibilityAndAgreesToPayForService: Coding = {
  code: '2',
  display: 'Patient has been informed of responsibility, and agrees to pay for service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0339',
};
const v2_0339_PatientHasBeenInformedOfResponsibilityAndAsksThatThePayerBeBilled: Coding = {
  code: '3',
  display: 'Patient has been informed of responsibility, and asks that the payer be billed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0339',
};
const v2_0339_AdvancedBeneficiaryNoticeHasNotBeenSigned: Coding = {
  code: '4',
  display: 'Advanced Beneficiary Notice has not been signed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0339',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0339 ( Advanced Beneficiary Notice Code)
 */
export const V20339 = {
  ServiceIsSubjectToMedicalNecessityProcedures: v2_0339_ServiceIsSubjectToMedicalNecessityProcedures,
  PatientHasBeenInformedOfResponsibilityAndAgreesToPayForService: v2_0339_PatientHasBeenInformedOfResponsibilityAndAgreesToPayForService,
  PatientHasBeenInformedOfResponsibilityAndAsksThatThePayerBeBilled: v2_0339_PatientHasBeenInformedOfResponsibilityAndAsksThatThePayerBeBilled,
  AdvancedBeneficiaryNoticeHasNotBeenSigned: v2_0339_AdvancedBeneficiaryNoticeHasNotBeenSigned,
};
const v2_0344_PatientIsInsured: Coding = {
  code: '01',
  display: 'Patient is insured',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_Spouse: Coding = {
  code: '02',
  display: 'Spouse',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_NaturalChildInsuredFinancialResponsibility: Coding = {
  code: '03',
  display: 'Natural child/insured financial responsibility',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_NaturalChildInsuredDoesNotHaveFinancialResponsibility: Coding = {
  code: '04',
  display: 'Natural child/Insured does not have financial responsibility',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_StepChild: Coding = {
  code: '05',
  display: 'Step child',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_FosterChild: Coding = {
  code: '06',
  display: 'Foster child',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_WardOfTheCourt: Coding = {
  code: '07',
  display: 'Ward of the court',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_Employee: Coding = {
  code: '08',
  display: 'Employee',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_Unknown: Coding = {
  code: '09',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_HandicappedDependent: Coding = {
  code: '10',
  display: 'Handicapped dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_OrganDonor: Coding = {
  code: '11',
  display: 'Organ donor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_CadaverDonor: Coding = {
  code: '12',
  display: 'Cadaver donor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_Grandchild: Coding = {
  code: '13',
  display: 'Grandchild',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_NieceNephew: Coding = {
  code: '14',
  display: 'Niece/nephew',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_InjuredPlaintiff: Coding = {
  code: '15',
  display: 'Injured plaintiff',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_SponsoredDependent: Coding = {
  code: '16',
  display: 'Sponsored dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_MinorDependentOfAMinorDependent: Coding = {
  code: '17',
  display: 'Minor dependent of a minor dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_Parent: Coding = {
  code: '18',
  display: 'Parent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
const v2_0344_Grandparent: Coding = {
  code: '19',
  display: 'Grandparent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0344',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0344 ( Patient’s relationship to insured)
 */
export const V20344 = {
  PatientIsInsured: v2_0344_PatientIsInsured,
  Spouse: v2_0344_Spouse,
  NaturalChildInsuredFinancialResponsibility: v2_0344_NaturalChildInsuredFinancialResponsibility,
  NaturalChildInsuredDoesNotHaveFinancialResponsibility: v2_0344_NaturalChildInsuredDoesNotHaveFinancialResponsibility,
  StepChild: v2_0344_StepChild,
  FosterChild: v2_0344_FosterChild,
  WardOfTheCourt: v2_0344_WardOfTheCourt,
  Employee: v2_0344_Employee,
  Unknown: v2_0344_Unknown,
  HandicappedDependent: v2_0344_HandicappedDependent,
  OrganDonor: v2_0344_OrganDonor,
  CadaverDonor: v2_0344_CadaverDonor,
  Grandchild: v2_0344_Grandchild,
  NieceNephew: v2_0344_NieceNephew,
  InjuredPlaintiff: v2_0344_InjuredPlaintiff,
  SponsoredDependent: v2_0344_SponsoredDependent,
  MinorDependentOfAMinorDependent: v2_0344_MinorDependentOfAMinorDependent,
  Parent: v2_0344_Parent,
  Grandparent: v2_0344_Grandparent,
};
const v2_0350_UseNUBCCodes: Coding = {
  code: '...',
  display: 'Use NUBC codes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0350',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0350 ( Occurrence code)
 */
export const V20350 = {
  UseNUBCCodes: v2_0350_UseNUBCCodes,
};
const v2_0351_UseNUBCCodes: Coding = {
  code: '...',
  display: 'use NUBC codes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0351',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0351 ( Occurrence span)
 */
export const V20351 = {
  UseNUBCCodes: v2_0351_UseNUBCCodes,
};
const v3_NullFlavor_NotApplicable: Coding = {
  code: 'NA',
  display: 'not applicable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_NullFlavor_NotAsked: Coding = {
  code: 'NASK',
  display: 'not asked',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_NullFlavor_TemporarilyUnavailable: Coding = {
  code: 'NAV',
  display: 'temporarily unavailable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0353 ( CWE statuses)
 */
export const V20353 = {
  /**
   * Known to have no proper value (e.g., last menstrual period for a male).
   */
  NotApplicable: v3_NullFlavor_NotApplicable,
  /**
   * This information has not been sought (e.g., patient was not asked)
   */
  NotAsked: v3_NullFlavor_NotAsked,
  /**
   * Information is not available at this time but it is expected that it will be available later.
   */
  TemporarilyUnavailable: v3_NullFlavor_TemporarilyUnavailable,
};
const v2_0354_Varies: Coding = {
  code: 'ACK',
  display: 'Varies',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_ADRA19: Coding = {
  code: 'ADR_A19',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A01A04A08A13: Coding = {
  code: 'ADT_A01',
  display: 'A01, A04, A08, A13',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A02: Coding = {
  code: 'ADT_A02',
  display: 'A02',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A03: Coding = {
  code: 'ADT_A03',
  display: 'A03',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A05A14A28A31: Coding = {
  code: 'ADT_A05',
  display: 'A05, A14, A28, A31',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A06A07: Coding = {
  code: 'ADT_A06',
  display: 'A06, A07',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A09A10A11: Coding = {
  code: 'ADT_A09',
  display: 'A09, A10, A11',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A12: Coding = {
  code: 'ADT_A12',
  display: 'A12',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A15: Coding = {
  code: 'ADT_A15',
  display: 'A15',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A16: Coding = {
  code: 'ADT_A16',
  display: 'A16',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A17: Coding = {
  code: 'ADT_A17',
  display: 'A17',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_ADTA18: Coding = {
  code: 'ADT_A18',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A20: Coding = {
  code: 'ADT_A20',
  display: 'A20',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A21A22A23A25A26A27A29A32A33: Coding = {
  code: 'ADT_A21',
  display: 'A21, A22, A23, A25, A26, A27, A29, A32, A33',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A24: Coding = {
  code: 'ADT_A24',
  display: 'A24',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_ADTA30: Coding = {
  code: 'ADT_A30',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A37: Coding = {
  code: 'ADT_A37',
  display: 'A37',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A38: Coding = {
  code: 'ADT_A38',
  display: 'A38',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A39A40A41A42: Coding = {
  code: 'ADT_A39',
  display: 'A39, A40, A41, A42',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A43: Coding = {
  code: 'ADT_A43',
  display: 'A43',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A44: Coding = {
  code: 'ADT_A44',
  display: 'A44',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A45: Coding = {
  code: 'ADT_A45',
  display: 'A45',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A50A51: Coding = {
  code: 'ADT_A50',
  display: 'A50, A51',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A52A53: Coding = {
  code: 'ADT_A52',
  display: 'A52, A53',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A54A55: Coding = {
  code: 'ADT_A54',
  display: 'A54, A55',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A60: Coding = {
  code: 'ADT_A60',
  display: 'A60',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_A61A62: Coding = {
  code: 'ADT_A61',
  display: 'A61, A62',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_P01: Coding = {
  code: 'BAR_P01',
  display: 'P01',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_P02: Coding = {
  code: 'BAR_P02',
  display: 'P02',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_P05: Coding = {
  code: 'BAR_P05',
  display: 'P05',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_P06: Coding = {
  code: 'BAR_P06',
  display: 'P06',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_P10: Coding = {
  code: 'BAR_P10',
  display: 'P10',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_P12: Coding = {
  code: 'BAR_P12',
  display: 'P12',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O29: Coding = {
  code: 'BPS_O29',
  display: 'O29',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O30: Coding = {
  code: 'BRP_O30',
  display: 'O30',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O32: Coding = {
  code: 'BRT_O32',
  display: 'O32',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O31: Coding = {
  code: 'BTS_O31',
  display: 'O31',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I22: Coding = {
  code: 'CCF_I22',
  display: 'I22',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I22_2: Coding = {
  code: 'CCI_I22',
  display: 'I22',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I21: Coding = {
  code: 'CCM_I21',
  display: 'I21',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I19: Coding = {
  code: 'CCQ_I19',
  display: 'I19',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I16Pipe17Pipe18: Coding = {
  code: 'CCR_I16',
  display: 'I16, |17, |18',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I20: Coding = {
  code: 'CCU_I20',
  display: 'I20',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I19_2: Coding = {
  code: 'CQU_I19',
  display: 'I19',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_C01C02C03C04C05C06C07C08: Coding = {
  code: 'CRM_C01',
  display: 'C01, C02, C03, C04, C05, C06, C07, C08',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_C09C10C11C12: Coding = {
  code: 'CSU_C09',
  display: 'C09, C10, C11, C12',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O41: Coding = {
  code: 'DBC_O41',
  display: 'O41',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O42: Coding = {
  code: 'DBC_O42',
  display: 'O42',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O46: Coding = {
  code: 'DEL_O46',
  display: 'O46',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O45: Coding = {
  code: 'DEO_O45',
  display: 'O45',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O44: Coding = {
  code: 'DER_O44',
  display: 'O44',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_P03: Coding = {
  code: 'DFT_P03',
  display: 'P03',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_P11: Coding = {
  code: 'DFT_P11',
  display: 'P11',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_DOCT12: Coding = {
  code: 'DOC_T12',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O48: Coding = {
  code: 'DPR_O48',
  display: 'O48',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O47: Coding = {
  code: 'DRC_O47',
  display: 'O47',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O43: Coding = {
  code: 'DRG_O43',
  display: 'O43',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_U07: Coding = {
  code: 'EAC_U07',
  display: 'U07',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_U09: Coding = {
  code: 'EAN_U09',
  display: 'U09',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_U08: Coding = {
  code: 'EAR_U08',
  display: 'U08',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E01: Coding = {
  code: 'EHC_E01',
  display: 'E01',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E02: Coding = {
  code: 'EHC_E02',
  display: 'E02',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E04: Coding = {
  code: 'EHC_E04',
  display: 'E04',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E10: Coding = {
  code: 'EHC_E10',
  display: 'E10',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E12: Coding = {
  code: 'EHC_E12',
  display: 'E12',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E13: Coding = {
  code: 'EHC_E13',
  display: 'E13',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E15: Coding = {
  code: 'EHC_E15',
  display: 'E15',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E20: Coding = {
  code: 'EHC_E20',
  display: 'E20',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E21: Coding = {
  code: 'EHC_E21',
  display: 'E21',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E24: Coding = {
  code: 'EHC_E24',
  display: 'E24',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_U02: Coding = {
  code: 'ESR_U02',
  display: 'U02',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_U01: Coding = {
  code: 'ESU_U01',
  display: 'U01',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_U06: Coding = {
  code: 'INR_U06',
  display: 'U06',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_U05: Coding = {
  code: 'INU_U05',
  display: 'U05',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_EventsU14: Coding = {
  code: 'INV_U14',
  display: 'Events U14',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_U12U13: Coding = {
  code: 'LSU_U12',
  display: 'U12, U13',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_T01T03T05T07T09T11: Coding = {
  code: 'MDM_T01',
  display: 'T01, T03, T05, T07, T09, T11',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_T02T04T06T08T10: Coding = {
  code: 'MDM_T02',
  display: 'T02, T04, T06, T08, T10',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M01M02M03M04M05M06M07M08M09M10M11: Coding = {
  code: 'MFK_M01',
  display: 'M01, M02, M03, M04, M05, M06, M07, M08, M09, M10, M11',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_MFNM01: Coding = {
  code: 'MFN_M01',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M02: Coding = {
  code: 'MFN_M02',
  display: 'M02',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M04: Coding = {
  code: 'MFN_M04',
  display: 'M04',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M05: Coding = {
  code: 'MFN_M05',
  display: 'M05',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M06: Coding = {
  code: 'MFN_M06',
  display: 'M06',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M07: Coding = {
  code: 'MFN_M07',
  display: 'M07',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M08: Coding = {
  code: 'MFN_M08',
  display: 'M08',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M09: Coding = {
  code: 'MFN_M09',
  display: 'M09',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M10: Coding = {
  code: 'MFN_M10',
  display: 'M10',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M11: Coding = {
  code: 'MFN_M11',
  display: 'M11',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M12: Coding = {
  code: 'MFN_M12',
  display: 'M12',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M13: Coding = {
  code: 'MFN_M13',
  display: 'M13',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M15: Coding = {
  code: 'MFN_M15',
  display: 'M15',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M16: Coding = {
  code: 'MFN_M16',
  display: 'M16',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M17: Coding = {
  code: 'MFN_M17',
  display: 'M17',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_M18: Coding = {
  code: 'MFN_M18',
  display: 'M18',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_N02: Coding = {
  code: 'NMD_N02',
  display: 'N02',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O27: Coding = {
  code: 'OMB_O27',
  display: 'O27',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O03: Coding = {
  code: 'OMD_O03',
  display: 'O03',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O19: Coding = {
  code: 'OMG_O19',
  display: 'O19',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O23: Coding = {
  code: 'OMI_O23',
  display: 'O23',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O21: Coding = {
  code: 'OML_O21',
  display: 'O21',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O33: Coding = {
  code: 'OML_O33',
  display: 'O33',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O35: Coding = {
  code: 'OML_O35',
  display: 'O35',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O39: Coding = {
  code: 'OML_O39',
  display: 'O39',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_LaboratoryFulfillmentRequestWithRELSegment: Coding = {
  code: 'OML_O59',
  display: 'Laboratory Fulfillment request with REL segment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O07: Coding = {
  code: 'OMN_O07',
  display: 'O07',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O09: Coding = {
  code: 'OMP_O09',
  display: 'O09',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O42_2: Coding = {
  code: 'OMQ_O42',
  display: 'O42',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O57: Coding = {
  code: 'OMQ_O57',
  display: 'O57',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O05: Coding = {
  code: 'OMS_O05',
  display: 'O05',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O37: Coding = {
  code: 'OPL_O37',
  display: 'O37',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O38: Coding = {
  code: 'OPR_O38',
  display: 'O38',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_R25: Coding = {
  code: 'OPU_R25',
  display: 'R25',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_R33: Coding = {
  code: 'ORA_R33',
  display: 'R33',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_R41: Coding = {
  code: 'ORA_R41',
  display: 'R41',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O28: Coding = {
  code: 'ORB_O28',
  display: 'O28',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O04: Coding = {
  code: 'ORD_O04',
  display: 'O04',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O20: Coding = {
  code: 'ORG_O20',
  display: 'O20',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O24: Coding = {
  code: 'ORI_O24',
  display: 'O24',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O22: Coding = {
  code: 'ORL_O22',
  display: 'O22',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O34: Coding = {
  code: 'ORL_O34',
  display: 'O34',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O36: Coding = {
  code: 'ORL_O36',
  display: 'O36',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O40: Coding = {
  code: 'ORL_O40',
  display: 'O40',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O41_2: Coding = {
  code: 'ORL_O41',
  display: 'O41',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O42_3: Coding = {
  code: 'ORL_O42',
  display: 'O42',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O43_2: Coding = {
  code: 'ORL_O43',
  display: 'O43',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O44_2: Coding = {
  code: 'ORL_O44',
  display: 'O44',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O53: Coding = {
  code: 'ORL_O53',
  display: 'O53',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O54: Coding = {
  code: 'ORL_O54',
  display: 'O54',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O55: Coding = {
  code: 'ORL_O55',
  display: 'O55',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O56: Coding = {
  code: 'ORL_O56',
  display: 'O56',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O08: Coding = {
  code: 'ORN_O08',
  display: 'O08',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O10: Coding = {
  code: 'ORP_O10',
  display: 'O10',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O06: Coding = {
  code: 'ORS_O06',
  display: 'O06',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_R01: Coding = {
  code: 'ORU_R01',
  display: 'R01',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_R30: Coding = {
  code: 'ORU_R30',
  display: 'R30',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O43_3: Coding = {
  code: 'ORX_O43',
  display: 'O43',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O58: Coding = {
  code: 'ORX_O58',
  display: 'O58',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_R26: Coding = {
  code: 'OSM_R26',
  display: 'R26',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O41_3: Coding = {
  code: 'OSU_O41',
  display: 'O41',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O51: Coding = {
  code: 'OSU_O51',
  display: 'O51',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O52: Coding = {
  code: 'OSU_O52',
  display: 'O52',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_R22: Coding = {
  code: 'OUL_R22',
  display: 'R22',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_R23: Coding = {
  code: 'OUL_R23',
  display: 'R23',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_R24: Coding = {
  code: 'OUL_R24',
  display: 'R24',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_P07P08: Coding = {
  code: 'PEX_P07',
  display: 'P07, P08',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_PC6PC7PC8: Coding = {
  code: 'PGL_PC6',
  display: 'PC6, PC7, PC8',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_B01B02: Coding = {
  code: 'PMU_B01',
  display: 'B01, B02',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_B03: Coding = {
  code: 'PMU_B03',
  display: 'B03',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_B04B05B06: Coding = {
  code: 'PMU_B04',
  display: 'B04, B05, B06',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_B07: Coding = {
  code: 'PMU_B07',
  display: 'B07',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_B08: Coding = {
  code: 'PMU_B08',
  display: 'B08',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_PCCPCGPCHPCJ: Coding = {
  code: 'PPG_PCG',
  display: 'PCC, PCG, PCH, PCJ',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_PCBPCD: Coding = {
  code: 'PPP_PCB',
  display: 'PCB, PCD',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_PC1PC2PC3: Coding = {
  code: 'PPR_PC1',
  display: 'PC1, PC2, PC3',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E03: Coding = {
  code: 'QBP_E03',
  display: 'E03',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E22: Coding = {
  code: 'QBP_E22',
  display: 'E22',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O33_2: Coding = {
  code: 'QBP_O33',
  display: 'O33',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O34_2: Coding = {
  code: 'QBP_O34',
  display: 'O34',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_Q11: Coding = {
  code: 'QBP_Q11',
  display: 'Q11',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_Q13: Coding = {
  code: 'QBP_Q13',
  display: 'Q13',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_Q15: Coding = {
  code: 'QBP_Q15',
  display: 'Q15',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_Q21Q22Q23Q24Q25: Coding = {
  code: 'QBP_Q21',
  display: 'Q21, Q22, Q23,Q24, Q25',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_J01J02: Coding = {
  code: 'QCN_J01',
  display: 'J01, J02',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_Q16: Coding = {
  code: 'QSB_Q16',
  display: 'Q16',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_Q17: Coding = {
  code: 'QVR_Q17',
  display: 'Q17',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O17: Coding = {
  code: 'RAS_O17',
  display: 'O17',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O11O25: Coding = {
  code: 'RDE_O11',
  display: 'O11, O25',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_EventsO49: Coding = {
  code: 'RDE_O49',
  display: 'Events O49',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_RDR: Coding = {
  code: 'RDR_RDR',
  display: 'RDR',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O13: Coding = {
  code: 'RDS_O13',
  display: 'O13',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_K15: Coding = {
  code: 'RDY_K15',
  display: 'K15',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I12I13I14I15: Coding = {
  code: 'REF_I12',
  display: 'I12, I13, I14, I15',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O15: Coding = {
  code: 'RGV_O15',
  display: 'O15',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I08I09I10I11: Coding = {
  code: 'RPA_I08',
  display: 'I08, I09. I10, I11',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I01I04: Coding = {
  code: 'RPI_I01',
  display: 'I01, I04',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I04: Coding = {
  code: 'RPI_I04',
  display: 'I04',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I02: Coding = {
  code: 'RPL_I02',
  display: 'I02',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I03: Coding = {
  code: 'RPR_I03',
  display: 'I03',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I08I09I10I11_2: Coding = {
  code: 'RQA_I08',
  display: 'I08, I09, I10, I11',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I01I02I03I07: Coding = {
  code: 'RQI_I01',
  display: 'I01, I02, I03, I07',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I04_2: Coding = {
  code: 'RQP_I04',
  display: 'I04',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O18: Coding = {
  code: 'RRA_O18',
  display: 'O18',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O14: Coding = {
  code: 'RRD_O14',
  display: 'O14',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O12O26: Coding = {
  code: 'RRE_O12',
  display: 'O12, O26',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_EventsO50: Coding = {
  code: 'RRE_O50',
  display: 'Events O50',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O16: Coding = {
  code: 'RRG_O16',
  display: 'O16',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_I12I13I14I15_2: Coding = {
  code: 'RRI_I12',
  display: 'I12, I13, I14, I15',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E03_2: Coding = {
  code: 'RSP_E03',
  display: 'E03',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_E22_2: Coding = {
  code: 'RSP_E22',
  display: 'E22',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_K11: Coding = {
  code: 'RSP_K11',
  display: 'K11',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_K21: Coding = {
  code: 'RSP_K21',
  display: 'K21',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_K22: Coding = {
  code: 'RSP_K22',
  display: 'K22',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_K23K24: Coding = {
  code: 'RSP_K23',
  display: 'K23, K24',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_K25: Coding = {
  code: 'RSP_K25',
  display: 'K25',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_K31: Coding = {
  code: 'RSP_K31',
  display: 'K31',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_K32: Coding = {
  code: 'RSP_K32',
  display: 'K32',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O33_3: Coding = {
  code: 'RSP_O33',
  display: 'O33',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_O34_3: Coding = {
  code: 'RSP_O34',
  display: 'O34',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_K13: Coding = {
  code: 'RTB_K13',
  display: 'K13',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_S31S36: Coding = {
  code: 'SDR_S31',
  display: 'S31, S36',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_S32S37: Coding = {
  code: 'SDR_S32',
  display: 'S32, S37',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_S12S13S14S15S16S17S18S19S20S21S22S23S24S26: Coding = {
  code: 'SIU_S12',
  display: 'S12, S13, S14, S15, S16, S17, S18, S19, S20, S21, S22, S23, S24, S26',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_S28S29S30S34S35: Coding = {
  code: 'SLR_S28',
  display: 'S28, S29, S30, S34, S35',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_S01S02S03S04S05S06S07S08S09S10S11: Coding = {
  code: 'SRM_S01',
  display: 'S01, S02, S03, S04, S05, S06, S07, S08, S09, S10, S11',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_S01S02S03S04S05S06S07S08S09S10S11_2: Coding = {
  code: 'SRR_S01',
  display: 'S01, S02, S03, S04, S05, S06, S07, S08, S09, S10, S11',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_U04: Coding = {
  code: 'SSR_U04',
  display: 'U04',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_U03: Coding = {
  code: 'SSU_U03',
  display: 'U03',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_S33: Coding = {
  code: 'STC_S33',
  display: 'S33',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_U10U11: Coding = {
  code: 'TCU_U10',
  display: 'U10, U11',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_Q05: Coding = {
  code: 'UDM_Q05',
  display: 'Q05',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
const v2_0354_V04: Coding = {
  code: 'VXU_V04',
  display: 'V04',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0354',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0354 ( Message structure)
 */
export const V20354 = {
  Varies: v2_0354_Varies,
  ADRA19: v2_0354_ADRA19,
  A01A04A08A13: v2_0354_A01A04A08A13,
  A02: v2_0354_A02,
  A03: v2_0354_A03,
  A05A14A28A31: v2_0354_A05A14A28A31,
  A06A07: v2_0354_A06A07,
  A09A10A11: v2_0354_A09A10A11,
  A12: v2_0354_A12,
  A15: v2_0354_A15,
  A16: v2_0354_A16,
  A17: v2_0354_A17,
  ADTA18: v2_0354_ADTA18,
  A20: v2_0354_A20,
  A21A22A23A25A26A27A29A32A33: v2_0354_A21A22A23A25A26A27A29A32A33,
  A24: v2_0354_A24,
  ADTA30: v2_0354_ADTA30,
  A37: v2_0354_A37,
  A38: v2_0354_A38,
  A39A40A41A42: v2_0354_A39A40A41A42,
  A43: v2_0354_A43,
  A44: v2_0354_A44,
  A45: v2_0354_A45,
  A50A51: v2_0354_A50A51,
  A52A53: v2_0354_A52A53,
  A54A55: v2_0354_A54A55,
  A60: v2_0354_A60,
  A61A62: v2_0354_A61A62,
  P01: v2_0354_P01,
  P02: v2_0354_P02,
  P05: v2_0354_P05,
  P06: v2_0354_P06,
  P10: v2_0354_P10,
  P12: v2_0354_P12,
  O29: v2_0354_O29,
  O30: v2_0354_O30,
  O32: v2_0354_O32,
  O31: v2_0354_O31,
  I22: v2_0354_I22,
  I22_2: v2_0354_I22_2,
  I21: v2_0354_I21,
  I19: v2_0354_I19,
  I16Pipe17Pipe18: v2_0354_I16Pipe17Pipe18,
  I20: v2_0354_I20,
  I19_2: v2_0354_I19_2,
  C01C02C03C04C05C06C07C08: v2_0354_C01C02C03C04C05C06C07C08,
  C09C10C11C12: v2_0354_C09C10C11C12,
  O41: v2_0354_O41,
  O42: v2_0354_O42,
  O46: v2_0354_O46,
  O45: v2_0354_O45,
  O44: v2_0354_O44,
  P03: v2_0354_P03,
  P11: v2_0354_P11,
  DOCT12: v2_0354_DOCT12,
  O48: v2_0354_O48,
  O47: v2_0354_O47,
  O43: v2_0354_O43,
  U07: v2_0354_U07,
  U09: v2_0354_U09,
  U08: v2_0354_U08,
  E01: v2_0354_E01,
  E02: v2_0354_E02,
  E04: v2_0354_E04,
  E10: v2_0354_E10,
  E12: v2_0354_E12,
  E13: v2_0354_E13,
  E15: v2_0354_E15,
  E20: v2_0354_E20,
  E21: v2_0354_E21,
  E24: v2_0354_E24,
  U02: v2_0354_U02,
  U01: v2_0354_U01,
  U06: v2_0354_U06,
  U05: v2_0354_U05,
  EventsU14: v2_0354_EventsU14,
  U12U13: v2_0354_U12U13,
  T01T03T05T07T09T11: v2_0354_T01T03T05T07T09T11,
  T02T04T06T08T10: v2_0354_T02T04T06T08T10,
  M01M02M03M04M05M06M07M08M09M10M11: v2_0354_M01M02M03M04M05M06M07M08M09M10M11,
  MFNM01: v2_0354_MFNM01,
  M02: v2_0354_M02,
  M04: v2_0354_M04,
  M05: v2_0354_M05,
  M06: v2_0354_M06,
  M07: v2_0354_M07,
  M08: v2_0354_M08,
  M09: v2_0354_M09,
  M10: v2_0354_M10,
  M11: v2_0354_M11,
  M12: v2_0354_M12,
  M13: v2_0354_M13,
  M15: v2_0354_M15,
  M16: v2_0354_M16,
  M17: v2_0354_M17,
  M18: v2_0354_M18,
  N02: v2_0354_N02,
  O27: v2_0354_O27,
  O03: v2_0354_O03,
  O19: v2_0354_O19,
  O23: v2_0354_O23,
  O21: v2_0354_O21,
  O33: v2_0354_O33,
  O35: v2_0354_O35,
  O39: v2_0354_O39,
  LaboratoryFulfillmentRequestWithRELSegment: v2_0354_LaboratoryFulfillmentRequestWithRELSegment,
  O07: v2_0354_O07,
  O09: v2_0354_O09,
  O42_2: v2_0354_O42_2,
  O57: v2_0354_O57,
  O05: v2_0354_O05,
  O37: v2_0354_O37,
  O38: v2_0354_O38,
  R25: v2_0354_R25,
  R33: v2_0354_R33,
  R41: v2_0354_R41,
  O28: v2_0354_O28,
  O04: v2_0354_O04,
  O20: v2_0354_O20,
  O24: v2_0354_O24,
  O22: v2_0354_O22,
  O34: v2_0354_O34,
  O36: v2_0354_O36,
  O40: v2_0354_O40,
  O41_2: v2_0354_O41_2,
  O42_3: v2_0354_O42_3,
  O43_2: v2_0354_O43_2,
  O44_2: v2_0354_O44_2,
  O53: v2_0354_O53,
  O54: v2_0354_O54,
  O55: v2_0354_O55,
  O56: v2_0354_O56,
  O08: v2_0354_O08,
  O10: v2_0354_O10,
  O06: v2_0354_O06,
  R01: v2_0354_R01,
  R30: v2_0354_R30,
  O43_3: v2_0354_O43_3,
  O58: v2_0354_O58,
  R26: v2_0354_R26,
  O41_3: v2_0354_O41_3,
  O51: v2_0354_O51,
  O52: v2_0354_O52,
  R22: v2_0354_R22,
  R23: v2_0354_R23,
  R24: v2_0354_R24,
  P07P08: v2_0354_P07P08,
  PC6PC7PC8: v2_0354_PC6PC7PC8,
  B01B02: v2_0354_B01B02,
  B03: v2_0354_B03,
  B04B05B06: v2_0354_B04B05B06,
  B07: v2_0354_B07,
  B08: v2_0354_B08,
  PCCPCGPCHPCJ: v2_0354_PCCPCGPCHPCJ,
  PCBPCD: v2_0354_PCBPCD,
  PC1PC2PC3: v2_0354_PC1PC2PC3,
  E03: v2_0354_E03,
  E22: v2_0354_E22,
  O33_2: v2_0354_O33_2,
  O34_2: v2_0354_O34_2,
  Q11: v2_0354_Q11,
  Q13: v2_0354_Q13,
  Q15: v2_0354_Q15,
  Q21Q22Q23Q24Q25: v2_0354_Q21Q22Q23Q24Q25,
  J01J02: v2_0354_J01J02,
  Q16: v2_0354_Q16,
  Q17: v2_0354_Q17,
  O17: v2_0354_O17,
  O11O25: v2_0354_O11O25,
  EventsO49: v2_0354_EventsO49,
  RDR: v2_0354_RDR,
  O13: v2_0354_O13,
  K15: v2_0354_K15,
  I12I13I14I15: v2_0354_I12I13I14I15,
  O15: v2_0354_O15,
  I08I09I10I11: v2_0354_I08I09I10I11,
  I01I04: v2_0354_I01I04,
  I04: v2_0354_I04,
  I02: v2_0354_I02,
  I03: v2_0354_I03,
  I08I09I10I11_2: v2_0354_I08I09I10I11_2,
  I01I02I03I07: v2_0354_I01I02I03I07,
  I04_2: v2_0354_I04_2,
  O18: v2_0354_O18,
  O14: v2_0354_O14,
  O12O26: v2_0354_O12O26,
  EventsO50: v2_0354_EventsO50,
  O16: v2_0354_O16,
  I12I13I14I15_2: v2_0354_I12I13I14I15_2,
  E03_2: v2_0354_E03_2,
  E22_2: v2_0354_E22_2,
  K11: v2_0354_K11,
  K21: v2_0354_K21,
  K22: v2_0354_K22,
  K23K24: v2_0354_K23K24,
  K25: v2_0354_K25,
  K31: v2_0354_K31,
  K32: v2_0354_K32,
  O33_3: v2_0354_O33_3,
  O34_3: v2_0354_O34_3,
  K13: v2_0354_K13,
  S31S36: v2_0354_S31S36,
  S32S37: v2_0354_S32S37,
  S12S13S14S15S16S17S18S19S20S21S22S23S24S26: v2_0354_S12S13S14S15S16S17S18S19S20S21S22S23S24S26,
  S28S29S30S34S35: v2_0354_S28S29S30S34S35,
  S01S02S03S04S05S06S07S08S09S10S11: v2_0354_S01S02S03S04S05S06S07S08S09S10S11,
  S01S02S03S04S05S06S07S08S09S10S11_2: v2_0354_S01S02S03S04S05S06S07S08S09S10S11_2,
  U04: v2_0354_U04,
  U03: v2_0354_U03,
  S33: v2_0354_S33,
  U10U11: v2_0354_U10U11,
  Q05: v2_0354_Q05,
  V04: v2_0354_V04,
};
const v2_0355_CodedElement: Coding = {
  code: 'CE',
  display: 'Coded element',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0355',
};
const v2_0355_CodedWithExceptions: Coding = {
  code: 'CWE',
  display: 'Coded with Exceptions',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0355',
};
const v2_0355_PersonLocation: Coding = {
  code: 'PL',
  display: 'Person location',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0355',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0355 ( Primary key value type)
 */
export const V20355 = {
  CodedElement: v2_0355_CodedElement,
  CodedWithExceptions: v2_0355_CodedWithExceptions,
  PersonLocation: v2_0355_PersonLocation,
};
const v2_0356_ThisIsTheDefaultIndicatingThatThereIsNoCharacterSetSwitchingOccurringInThisMessage: Coding = {
  code: '<null>',
  display: 'This is the default, indicating that there is no character set switching occurring in this message.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0356',
};
const v2_0356_TheCharacterSetSwitchingModeSpecifiedInHL725Section272AndSection2A46QuotationXPNExtendedPersonNameQuotation: Coding = {
  code: '2.3',
  display: 'The character set switching mode specified in HL7 2.5, section 2.7.2 and section 2.A.46, "XPN - extended person name".',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0356',
};
const v2_0356_ThisStandardIsTitledQuotationInformationTechnologyCharacterCodeStructureAndExtensionTechniqueQuotation: Coding = {
  code: 'ISO 2022-1994',
  display: 'This standard is titled "Information Technology - Character Code Structure and Extension Technique". .',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0356',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0356 ( Alternate character set handling scheme)
 */
export const V20356 = {
  ThisIsTheDefaultIndicatingThatThereIsNoCharacterSetSwitchingOccurringInThisMessage: v2_0356_ThisIsTheDefaultIndicatingThatThereIsNoCharacterSetSwitchingOccurringInThisMessage,
  TheCharacterSetSwitchingModeSpecifiedInHL725Section272AndSection2A46QuotationXPNExtendedPersonNameQuotation: v2_0356_TheCharacterSetSwitchingModeSpecifiedInHL725Section272AndSection2A46QuotationXPNExtendedPersonNameQuotation,
  ThisStandardIsTitledQuotationInformationTechnologyCharacterCodeStructureAndExtensionTechniqueQuotation: v2_0356_ThisStandardIsTitledQuotationInformationTechnologyCharacterCodeStructureAndExtensionTechniqueQuotation,
};
const v2_0357_MessageAccepted: Coding = {
  code: '0',
  display: 'Message accepted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_SegmentSequenceError: Coding = {
  code: '100',
  display: 'Segment sequence error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_RequiredFieldMissing: Coding = {
  code: '101',
  display: 'Required field missing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_DataTypeError: Coding = {
  code: '102',
  display: 'Data type error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_TableValueNotFound: Coding = {
  code: '103',
  display: 'Table value not found',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_ValueTooLong: Coding = {
  code: '104',
  display: 'Value too long',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_NonConformantCardinality: Coding = {
  code: '198',
  display: 'Non-Conformant Cardinality',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_OtherHL7Error: Coding = {
  code: '199',
  display: 'Other HL7 Error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_UnsupportedMessageType: Coding = {
  code: '200',
  display: 'Unsupported message type',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_UnsupportedEventCode: Coding = {
  code: '201',
  display: 'Unsupported event code',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_UnsupportedProcessingId: Coding = {
  code: '202',
  display: 'Unsupported processing id',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_UnsupportedVersionId: Coding = {
  code: '203',
  display: 'Unsupported version id',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_UnknownKeyIdentifier: Coding = {
  code: '204',
  display: 'Unknown key identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_DuplicateKeyIdentifier: Coding = {
  code: '205',
  display: 'Duplicate key identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_ApplicationRecordLocked: Coding = {
  code: '206',
  display: 'Application record locked',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
const v2_0357_ApplicationError: Coding = {
  code: '207',
  display: 'Application error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0357',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0357 ( Message error condition  codes)
 */
export const V20357 = {
  MessageAccepted: v2_0357_MessageAccepted,
  SegmentSequenceError: v2_0357_SegmentSequenceError,
  RequiredFieldMissing: v2_0357_RequiredFieldMissing,
  DataTypeError: v2_0357_DataTypeError,
  TableValueNotFound: v2_0357_TableValueNotFound,
  ValueTooLong: v2_0357_ValueTooLong,
  NonConformantCardinality: v2_0357_NonConformantCardinality,
  OtherHL7Error: v2_0357_OtherHL7Error,
  UnsupportedMessageType: v2_0357_UnsupportedMessageType,
  UnsupportedEventCode: v2_0357_UnsupportedEventCode,
  UnsupportedProcessingId: v2_0357_UnsupportedProcessingId,
  UnsupportedVersionId: v2_0357_UnsupportedVersionId,
  UnknownKeyIdentifier: v2_0357_UnknownKeyIdentifier,
  DuplicateKeyIdentifier: v2_0357_DuplicateKeyIdentifier,
  ApplicationRecordLocked: v2_0357_ApplicationRecordLocked,
  ApplicationError: v2_0357_ApplicationError,
};
const v2_0359_NotIncludedInDiagnosisRanking: Coding = {
  code: '0',
  display: 'Not included in diagnosis ranking',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0359',
};
const v2_0359_ThePrimaryDiagnosis: Coding = {
  code: '1',
  display: 'The primary diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0359',
};
const v2_0359_ForSecondaryDiagnosis: Coding = {
  code: '2',
  display: 'For  secondary diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0359',
};
const v2_0359_ForTertiaryDiagnosis: Coding = {
  code: '3',
  display: 'For tertiary diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0359',
};
const v2_0359_ForQuaternaryDiagnosis: Coding = {
  code: '4',
  display: 'For quaternary diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0359',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0359 ( Diagnosis priority)
 */
export const V20359 = {
  NotIncludedInDiagnosisRanking: v2_0359_NotIncludedInDiagnosisRanking,
  ThePrimaryDiagnosis: v2_0359_ThePrimaryDiagnosis,
  ForSecondaryDiagnosis: v2_0359_ForSecondaryDiagnosis,
  ForTertiaryDiagnosis: v2_0359_ForTertiaryDiagnosis,
  ForQuaternaryDiagnosis: v2_0359_ForQuaternaryDiagnosis,
};
const v2_0363_NoSuggestedValuesDefined: Coding = {
  code: '...',
  display: 'No suggested values defined',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0363',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0363 ( Assigning authority)
 */
export const V20363 = {
  NoSuggestedValuesDefined: v2_0363_NoSuggestedValuesDefined,
};
const v2_0364_PrimaryReason: Coding = {
  code: '1R',
  display: 'Primary Reason',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0364',
};
const v2_0364_SecondaryReason: Coding = {
  code: '2R',
  display: 'Secondary Reason',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0364',
};
const v2_0364_AncillaryInstructions: Coding = {
  code: 'AI',
  display: 'Ancillary Instructions',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0364',
};
const v2_0364_DuplicateInteractionReason: Coding = {
  code: 'DR',
  display: 'Duplicate/Interaction Reason',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0364',
};
const v2_0364_GeneralInstructions: Coding = {
  code: 'GI',
  display: 'General Instructions',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0364',
};
const v2_0364_GeneralReason: Coding = {
  code: 'GR',
  display: 'General Reason',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0364',
};
const v2_0364_PatientInstructions: Coding = {
  code: 'PI',
  display: 'Patient Instructions',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0364',
};
const v2_0364_Remark: Coding = {
  code: 'RE',
  display: 'Remark',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0364',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0364 ( Comment type)
 */
export const V20364 = {
  PrimaryReason: v2_0364_PrimaryReason,
  SecondaryReason: v2_0364_SecondaryReason,
  AncillaryInstructions: v2_0364_AncillaryInstructions,
  DuplicateInteractionReason: v2_0364_DuplicateInteractionReason,
  GeneralInstructions: v2_0364_GeneralInstructions,
  GeneralReason: v2_0364_GeneralReason,
  PatientInstructions: v2_0364_PatientInstructions,
  Remark: v2_0364_Remark,
};
const v2_0365_Clearing: Coding = {
  code: 'CL',
  display: 'Clearing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_Configuring: Coding = {
  code: 'CO',
  display: 'Configuring',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_Diagnose: Coding = {
  code: 'DI',
  display: 'Diagnose',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_EStopped: Coding = {
  code: 'ES',
  display: 'E-stopped',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_Failure: Coding = {
  code: 'FL',
  display: 'Failure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_Idle: Coding = {
  code: 'ID',
  display: 'Idle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_Initializing: Coding = {
  code: 'IN',
  display: 'Initializing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_LimitedTestMenu: Coding = {
  code: 'LT',
  display: 'Limited test menu',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_Maintenance: Coding = {
  code: 'MA',
  display: 'Maintenance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_NormalOperation: Coding = {
  code: 'OP',
  display: 'Normal Operation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_Pausing: Coding = {
  code: 'PA',
  display: 'Pausing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_Paused: Coding = {
  code: 'PD',
  display: 'Paused',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_PoweredUp: Coding = {
  code: 'PU',
  display: 'Powered Up',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_ReadyToStart: Coding = {
  code: 'RS',
  display: 'Ready to start',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_ShuttingDown: Coding = {
  code: 'SD',
  display: 'Shutting down',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_SamplingStopped: Coding = {
  code: 'SS',
  display: 'Sampling stopped',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_TransportStopped: Coding = {
  code: 'TS',
  display: 'Transport stopped',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
const v2_0365_Unknown: Coding = {
  code: 'UNK',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0365',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0365 ( Equipment state)
 */
export const V20365 = {
  Clearing: v2_0365_Clearing,
  Configuring: v2_0365_Configuring,
  Diagnose: v2_0365_Diagnose,
  EStopped: v2_0365_EStopped,
  Failure: v2_0365_Failure,
  Idle: v2_0365_Idle,
  Initializing: v2_0365_Initializing,
  LimitedTestMenu: v2_0365_LimitedTestMenu,
  Maintenance: v2_0365_Maintenance,
  NormalOperation: v2_0365_NormalOperation,
  Pausing: v2_0365_Pausing,
  Paused: v2_0365_Paused,
  PoweredUp: v2_0365_PoweredUp,
  ReadyToStart: v2_0365_ReadyToStart,
  ShuttingDown: v2_0365_ShuttingDown,
  SamplingStopped: v2_0365_SamplingStopped,
  TransportStopped: v2_0365_TransportStopped,
  Unknown: v2_0365_Unknown,
};
const v2_0366_Local: Coding = {
  code: 'L',
  display: 'Local',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0366',
};
const v2_0366_Remote: Coding = {
  code: 'R',
  display: 'Remote',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0366',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0366 ( Local/remote control state)
 */
export const V20366 = {
  Local: v2_0366_Local,
  Remote: v2_0366_Remote,
};
const v2_0367_Critical: Coding = {
  code: 'C',
  display: 'Critical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0367',
};
const v2_0367_Normal: Coding = {
  code: 'N',
  display: 'Normal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0367',
};
const v2_0367_Serious: Coding = {
  code: 'S',
  display: 'Serious',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0367',
};
const v2_0367_Warning: Coding = {
  code: 'W',
  display: 'Warning',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0367',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0367 ( Alert level)
 */
export const V20367 = {
  Critical: v2_0367_Critical,
  Normal: v2_0367_Normal,
  Serious: v2_0367_Serious,
  Warning: v2_0367_Warning,
};
const v2_0368_Abort: Coding = {
  code: 'AB',
  display: 'Abort',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_AliquotFromContainer: Coding = {
  code: 'AF',
  display: 'Aliquot From container',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_AliquotToContainer: Coding = {
  code: 'AT',
  display: 'Aliquot To container',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_Clear: Coding = {
  code: 'CL',
  display: 'Clear',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_ClearNotification: Coding = {
  code: 'CN',
  display: 'Clear Notification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_DisableSendingEvents: Coding = {
  code: 'DI',
  display: 'Disable Sending Events',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_EnableSendingEvents: Coding = {
  code: 'EN',
  display: 'Enable Sending Events',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_EmergencyStop: Coding = {
  code: 'ES',
  display: 'Emergency -stop',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_ExecuteCommandSpecifiedInFieldParametersST01394: Coding = {
  code: 'EX',
  display: 'Execute (command specified in field Parameters (ST) 01394)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_InitializeInitiate: Coding = {
  code: 'IN',
  display: 'Initialize/Initiate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_LocalControlRequest: Coding = {
  code: 'LC',
  display: 'Local Control Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_Lock: Coding = {
  code: 'LK',
  display: 'Lock',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_Load: Coding = {
  code: 'LO',
  display: 'Load',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_Pause: Coding = {
  code: 'PA',
  display: 'Pause',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_RemoteControlRequest: Coding = {
  code: 'RC',
  display: 'Remote Control Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_Resume: Coding = {
  code: 'RE',
  display: 'Resume',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_Sampling: Coding = {
  code: 'SA',
  display: 'Sampling',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_Setup: Coding = {
  code: 'SU',
  display: 'Setup',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_TransportTo: Coding = {
  code: 'TT',
  display: 'Transport To',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_Unlock: Coding = {
  code: 'UC',
  display: 'Unlock',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
const v2_0368_Unload: Coding = {
  code: 'UN',
  display: 'Unload',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0368',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0368 ( Remote control command)
 */
export const V20368 = {
  Abort: v2_0368_Abort,
  AliquotFromContainer: v2_0368_AliquotFromContainer,
  AliquotToContainer: v2_0368_AliquotToContainer,
  Clear: v2_0368_Clear,
  ClearNotification: v2_0368_ClearNotification,
  DisableSendingEvents: v2_0368_DisableSendingEvents,
  EnableSendingEvents: v2_0368_EnableSendingEvents,
  EmergencyStop: v2_0368_EmergencyStop,
  ExecuteCommandSpecifiedInFieldParametersST01394: v2_0368_ExecuteCommandSpecifiedInFieldParametersST01394,
  InitializeInitiate: v2_0368_InitializeInitiate,
  LocalControlRequest: v2_0368_LocalControlRequest,
  Lock: v2_0368_Lock,
  Load: v2_0368_Load,
  Pause: v2_0368_Pause,
  RemoteControlRequest: v2_0368_RemoteControlRequest,
  Resume: v2_0368_Resume,
  Sampling: v2_0368_Sampling,
  Setup: v2_0368_Setup,
  TransportTo: v2_0368_TransportTo,
  Unlock: v2_0368_Unlock,
  Unload: v2_0368_Unload,
};
const v2_0369_BlindSample: Coding = {
  code: 'B',
  display: 'Blind Sample',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0369',
};
const v2_0369_CalibratorUsedForInitialSettingOfCalibration: Coding = {
  code: 'C',
  display: 'Calibrator, used for initial setting of calibration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0369',
};
const v2_0369_ElectronicQCUsedWithManufacturedReferenceProvidingSignalsThatSimulateQCResults: Coding = {
  code: 'E',
  display: 'Electronic QC, used with manufactured reference providing signals that simulate QC results',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0369',
};
const v2_0369_SpecimenUsedForTestingProficiencyOfTheOrganizationPerformingTheTestingFiller: Coding = {
  code: 'F',
  display: 'Specimen used for testing proficiency of the organization performing the testing (Filler)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0369',
};
const v2_0369_GroupWhereASpecimenConsistsOfMultipleIndividualElementsThatAreNotIndividuallyIdentified: Coding = {
  code: 'G',
  display: 'Group (where a specimen consists of multiple individual elements that are not individually identified)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0369',
};
const v2_0369_PoolAliquotsOfIndividualSpecimensCombinedToFormASingleSpecimenRepresentingAllOfTheComponents: Coding = {
  code: 'L',
  display: 'Pool (aliquots of individual specimens combined to form a single specimen representing all of the components.)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0369',
};
const v2_0369_SpecimenUsedForTestingOperatorProficiency: Coding = {
  code: 'O',
  display: 'Specimen used for testing Operator Proficiency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0369',
};
const v2_0369_PatientDefaultIfBlankComponentValue: Coding = {
  code: 'P',
  display: 'Patient (default if blank component value)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0369',
};
const v2_0369_ControlSpecimen: Coding = {
  code: 'Q',
  display: 'Control specimen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0369',
};
const v2_0369_ReplicateOfPatientSampleAsAControl: Coding = {
  code: 'R',
  display: 'Replicate (of patient sample as a control)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0369',
};
const v2_0369_VerifyingCalibratorUsedForPeriodicCalibrationChecks: Coding = {
  code: 'V',
  display: 'Verifying Calibrator, used for periodic calibration checks',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0369',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0369 ( Specimen role)
 */
export const V20369 = {
  BlindSample: v2_0369_BlindSample,
  CalibratorUsedForInitialSettingOfCalibration: v2_0369_CalibratorUsedForInitialSettingOfCalibration,
  ElectronicQCUsedWithManufacturedReferenceProvidingSignalsThatSimulateQCResults: v2_0369_ElectronicQCUsedWithManufacturedReferenceProvidingSignalsThatSimulateQCResults,
  SpecimenUsedForTestingProficiencyOfTheOrganizationPerformingTheTestingFiller: v2_0369_SpecimenUsedForTestingProficiencyOfTheOrganizationPerformingTheTestingFiller,
  GroupWhereASpecimenConsistsOfMultipleIndividualElementsThatAreNotIndividuallyIdentified: v2_0369_GroupWhereASpecimenConsistsOfMultipleIndividualElementsThatAreNotIndividuallyIdentified,
  PoolAliquotsOfIndividualSpecimensCombinedToFormASingleSpecimenRepresentingAllOfTheComponents: v2_0369_PoolAliquotsOfIndividualSpecimensCombinedToFormASingleSpecimenRepresentingAllOfTheComponents,
  SpecimenUsedForTestingOperatorProficiency: v2_0369_SpecimenUsedForTestingOperatorProficiency,
  PatientDefaultIfBlankComponentValue: v2_0369_PatientDefaultIfBlankComponentValue,
  ControlSpecimen: v2_0369_ControlSpecimen,
  ReplicateOfPatientSampleAsAControl: v2_0369_ReplicateOfPatientSampleAsAControl,
  VerifyingCalibratorUsedForPeriodicCalibrationChecks: v2_0369_VerifyingCalibratorUsedForPeriodicCalibrationChecks,
};
const v2_0370_Archived: Coding = {
  code: 'A',
  display: 'Archived',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0370',
};
const v2_0370_Identified: Coding = {
  code: 'I',
  display: 'Identified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0370',
};
const v2_0370_LeftEquipment: Coding = {
  code: 'L',
  display: 'Left Equipment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0370',
};
const v2_0370_Missing: Coding = {
  code: 'M',
  display: 'Missing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0370',
};
const v2_0370_InProcess: Coding = {
  code: 'O',
  display: 'In Process',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0370',
};
const v2_0370_InPosition: Coding = {
  code: 'P',
  display: 'In Position',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0370',
};
const v2_0370_ProcessCompleted: Coding = {
  code: 'R',
  display: 'Process Completed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0370',
};
const v2_0370_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0370',
};
const v2_0370_ContainerUnavailable: Coding = {
  code: 'X',
  display: 'Container Unavailable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0370',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0370 ( Container status)
 */
export const V20370 = {
  Archived: v2_0370_Archived,
  Identified: v2_0370_Identified,
  LeftEquipment: v2_0370_LeftEquipment,
  Missing: v2_0370_Missing,
  InProcess: v2_0370_InProcess,
  InPosition: v2_0370_InPosition,
  ProcessCompleted: v2_0370_ProcessCompleted,
  Unknown: v2_0370_Unknown,
  ContainerUnavailable: v2_0370_ContainerUnavailable,
};
const v2_0371_ACDSolutionA: Coding = {
  code: 'ACDA',
  display: 'ACD Solution A',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_ACDSolutionB: Coding = {
  code: 'ACDB',
  display: 'ACD Solution B',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_AceticAcid: Coding = {
  code: 'ACET',
  display: 'Acetic Acid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_AmiesTransportMedium: Coding = {
  code: 'AMIES',
  display: 'Amies transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_BacterialTransportMedium: Coding = {
  code: 'BACTM',
  display: 'Bacterial Transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_Buffered10PercentFormalin: Coding = {
  code: 'BF10',
  display: 'Buffered 10% formalin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_BorateBoricAcid: Coding = {
  code: 'BOR',
  display: 'Borate Boric Acid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_BouinQuoteSSolution: Coding = {
  code: 'BOUIN',
  display: "Bouin's solution",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_BufferedSkimMilk: Coding = {
  code: 'BSKM',
  display: 'Buffered skim milk',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_VAL32PercentCitrate: Coding = {
  code: 'C32',
  display: '3.2%  Citrate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_VAL38PercentCitrate: Coding = {
  code: 'C38',
  display: '3.8% Citrate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_CarsonQuoteSModified10PercentFormalin: Coding = {
  code: 'CARS',
  display: "Carson's Modified 10% formalin",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_CaryBlairMedium: Coding = {
  code: 'CARY',
  display: 'Cary Blair Medium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_ChlamydiaTransportMedium: Coding = {
  code: 'CHLTM',
  display: 'Chlamydia transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_CTADThisShouldBeSpelledOutIfNotUniversallyUnderstood: Coding = {
  code: 'CTAD',
  display: 'CTAD (this should be spelled out if not universally understood)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_PotassiumKEDTA: Coding = {
  code: 'EDTK',
  display: 'Potassium/K EDTA',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_PotassiumKEDTA15Percent: Coding = {
  code: 'EDTK15',
  display: 'Potassium/K EDTA 15%',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_PotassiumKEDTA75Percent: Coding = {
  code: 'EDTK75',
  display: 'Potassium/K EDTA 7.5%',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_SodiumNaEDTA: Coding = {
  code: 'EDTN',
  display: 'Sodium/Na EDTA',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_EntericBacteriaTransportMedium: Coding = {
  code: 'ENT',
  display: 'Enteric bacteria transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_EntericPlus: Coding = {
  code: 'ENT+',
  display: 'Enteric plus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_VAL10PercentFormalin: Coding = {
  code: 'F10',
  display: '10% Formalin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_ThrombinNIHSoybeanTrypsinInhibitorFibrinDegradationProducts: Coding = {
  code: 'FDP',
  display: 'Thrombin NIH; soybean trypsin inhibitor (Fibrin Degradation Products)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_SodiumFluoride10mg: Coding = {
  code: 'FL10',
  display: 'Sodium Fluoride, 10mg',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_SodiumFluoride100mg: Coding = {
  code: 'FL100',
  display: 'Sodium Fluoride, 100mg',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_VAL6NHCL: Coding = {
  code: 'HCL6',
  display: '6N HCL',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_AmmoniumHeparin: Coding = {
  code: 'HEPA',
  display: 'Ammonium heparin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_LithiumLiHeparin: Coding = {
  code: 'HEPL',
  display: 'Lithium/Li  Heparin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_SodiumNaHeparin: Coding = {
  code: 'HEPN',
  display: 'Sodium/Na  Heparin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_NitricAcid: Coding = {
  code: 'HNO3',
  display: 'Nitric Acid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_JonesKendrickMedium: Coding = {
  code: 'JKM',
  display: 'Jones Kendrick Medium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_KarnovskyQuoteSFixative: Coding = {
  code: 'KARN',
  display: "Karnovsky's fixative",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_PotassiumOxalate: Coding = {
  code: 'KOX',
  display: 'Potassium Oxalate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_LithiumIodoacetate: Coding = {
  code: 'LIA',
  display: 'Lithium iodoacetate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_M4: Coding = {
  code: 'M4',
  display: 'M4',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_M4RT: Coding = {
  code: 'M4RT',
  display: 'M4-RT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_M5: Coding = {
  code: 'M5',
  display: 'M5',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_MichelQuoteSTransportMedium: Coding = {
  code: 'MICHTM',
  display: "Michel's transport medium",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_MMDTransportMedium: Coding = {
  code: 'MMDTM',
  display: 'MMD transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_SodiumFluoride: Coding = {
  code: 'NAF',
  display: 'Sodium Fluoride',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_SodiumPolyanetholSulfonate035PercentIn085PercentSodiumChloride: Coding = {
  code: 'NAPS',
  display: 'Sodium polyanethol sulfonate 0.35% in 0.85% sodium chloride',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_None: Coding = {
  code: 'NONE',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_PagesQuoteSSaline: Coding = {
  code: 'PAGE',
  display: "Pages's Saline",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_Phenol: Coding = {
  code: 'PHENOL',
  display: 'Phenol',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_PVAPolyvinylalcohol: Coding = {
  code: 'PVA',
  display: 'PVA (polyvinylalcohol)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_ReaganLoweMedium: Coding = {
  code: 'RLM',
  display: 'Reagan Lowe Medium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_SiliceousEarth12Mg: Coding = {
  code: 'SILICA',
  display: 'Siliceous earth, 12 mg',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_SPSThisShouldBeSpelledOutIfNotUniversallyUnderstood: Coding = {
  code: 'SPS',
  display: 'SPS(this should be spelled out if not universally understood)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_SerumSeparatorTubePolymerGel: Coding = {
  code: 'SST',
  display: 'Serum Separator Tube (Polymer Gel)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_StuartTransportMedium: Coding = {
  code: 'STUTM',
  display: 'Stuart transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_Thrombin: Coding = {
  code: 'THROM',
  display: 'Thrombin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_Thymol: Coding = {
  code: 'THYMOL',
  display: 'Thymol',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_ThyoglycollateBroth: Coding = {
  code: 'THYO',
  display: 'Thyoglycollate broth',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_Toluene: Coding = {
  code: 'TOLU',
  display: 'Toluene',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_UreaplasmaTransportMedium: Coding = {
  code: 'URETM',
  display: 'Ureaplasma transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_ViralTransportMedium: Coding = {
  code: 'VIRTM',
  display: 'Viral Transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
const v2_0371_BufferedCitrateWestergrenSedimentationRate: Coding = {
  code: 'WEST',
  display: 'Buffered Citrate (Westergren Sedimentation Rate)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0371',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0371 ( Additive)
 */
export const V20371 = {
  ACDSolutionA: v2_0371_ACDSolutionA,
  ACDSolutionB: v2_0371_ACDSolutionB,
  AceticAcid: v2_0371_AceticAcid,
  AmiesTransportMedium: v2_0371_AmiesTransportMedium,
  BacterialTransportMedium: v2_0371_BacterialTransportMedium,
  Buffered10PercentFormalin: v2_0371_Buffered10PercentFormalin,
  BorateBoricAcid: v2_0371_BorateBoricAcid,
  BouinQuoteSSolution: v2_0371_BouinQuoteSSolution,
  BufferedSkimMilk: v2_0371_BufferedSkimMilk,
  VAL32PercentCitrate: v2_0371_VAL32PercentCitrate,
  VAL38PercentCitrate: v2_0371_VAL38PercentCitrate,
  CarsonQuoteSModified10PercentFormalin: v2_0371_CarsonQuoteSModified10PercentFormalin,
  CaryBlairMedium: v2_0371_CaryBlairMedium,
  ChlamydiaTransportMedium: v2_0371_ChlamydiaTransportMedium,
  CTADThisShouldBeSpelledOutIfNotUniversallyUnderstood: v2_0371_CTADThisShouldBeSpelledOutIfNotUniversallyUnderstood,
  PotassiumKEDTA: v2_0371_PotassiumKEDTA,
  PotassiumKEDTA15Percent: v2_0371_PotassiumKEDTA15Percent,
  PotassiumKEDTA75Percent: v2_0371_PotassiumKEDTA75Percent,
  SodiumNaEDTA: v2_0371_SodiumNaEDTA,
  EntericBacteriaTransportMedium: v2_0371_EntericBacteriaTransportMedium,
  EntericPlus: v2_0371_EntericPlus,
  VAL10PercentFormalin: v2_0371_VAL10PercentFormalin,
  ThrombinNIHSoybeanTrypsinInhibitorFibrinDegradationProducts: v2_0371_ThrombinNIHSoybeanTrypsinInhibitorFibrinDegradationProducts,
  SodiumFluoride10mg: v2_0371_SodiumFluoride10mg,
  SodiumFluoride100mg: v2_0371_SodiumFluoride100mg,
  VAL6NHCL: v2_0371_VAL6NHCL,
  AmmoniumHeparin: v2_0371_AmmoniumHeparin,
  LithiumLiHeparin: v2_0371_LithiumLiHeparin,
  SodiumNaHeparin: v2_0371_SodiumNaHeparin,
  NitricAcid: v2_0371_NitricAcid,
  JonesKendrickMedium: v2_0371_JonesKendrickMedium,
  KarnovskyQuoteSFixative: v2_0371_KarnovskyQuoteSFixative,
  PotassiumOxalate: v2_0371_PotassiumOxalate,
  LithiumIodoacetate: v2_0371_LithiumIodoacetate,
  M4: v2_0371_M4,
  M4RT: v2_0371_M4RT,
  M5: v2_0371_M5,
  MichelQuoteSTransportMedium: v2_0371_MichelQuoteSTransportMedium,
  MMDTransportMedium: v2_0371_MMDTransportMedium,
  SodiumFluoride: v2_0371_SodiumFluoride,
  SodiumPolyanetholSulfonate035PercentIn085PercentSodiumChloride: v2_0371_SodiumPolyanetholSulfonate035PercentIn085PercentSodiumChloride,
  None: v2_0371_None,
  PagesQuoteSSaline: v2_0371_PagesQuoteSSaline,
  Phenol: v2_0371_Phenol,
  PVAPolyvinylalcohol: v2_0371_PVAPolyvinylalcohol,
  ReaganLoweMedium: v2_0371_ReaganLoweMedium,
  SiliceousEarth12Mg: v2_0371_SiliceousEarth12Mg,
  SPSThisShouldBeSpelledOutIfNotUniversallyUnderstood: v2_0371_SPSThisShouldBeSpelledOutIfNotUniversallyUnderstood,
  SerumSeparatorTubePolymerGel: v2_0371_SerumSeparatorTubePolymerGel,
  StuartTransportMedium: v2_0371_StuartTransportMedium,
  Thrombin: v2_0371_Thrombin,
  Thymol: v2_0371_Thymol,
  ThyoglycollateBroth: v2_0371_ThyoglycollateBroth,
  Toluene: v2_0371_Toluene,
  UreaplasmaTransportMedium: v2_0371_UreaplasmaTransportMedium,
  ViralTransportMedium: v2_0371_ViralTransportMedium,
  BufferedCitrateWestergrenSedimentationRate: v2_0371_BufferedCitrateWestergrenSedimentationRate,
};
const v2_0372_WholeBloodHomogeneous: Coding = {
  code: 'BLD',
  display: 'Whole blood, homogeneous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0372',
};
const v2_0372_WholeBloodSeparated: Coding = {
  code: 'BSEP',
  display: 'Whole blood, separated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0372',
};
const v2_0372_PlasmaNOSNotOtherwiseSpecified: Coding = {
  code: 'PLAS',
  display: 'Plasma, NOS (not otherwise specified)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0372',
};
const v2_0372_PlateletPoorPlasma: Coding = {
  code: 'PPP',
  display: 'Platelet poor plasma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0372',
};
const v2_0372_PlateletRichPlasma: Coding = {
  code: 'PRP',
  display: 'Platelet rich plasma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0372',
};
const v2_0372_Sediment: Coding = {
  code: 'SED',
  display: 'Sediment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0372',
};
const v2_0372_SerumNOSNotOtherwiseSpecified: Coding = {
  code: 'SER',
  display: 'Serum, NOS (not otherwise specified)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0372',
};
const v2_0372_Supernatant: Coding = {
  code: 'SUP',
  display: 'Supernatant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0372',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0372 ( Specimen component)
 */
export const V20372 = {
  WholeBloodHomogeneous: v2_0372_WholeBloodHomogeneous,
  WholeBloodSeparated: v2_0372_WholeBloodSeparated,
  PlasmaNOSNotOtherwiseSpecified: v2_0372_PlasmaNOSNotOtherwiseSpecified,
  PlateletPoorPlasma: v2_0372_PlateletPoorPlasma,
  PlateletRichPlasma: v2_0372_PlateletRichPlasma,
  Sediment: v2_0372_Sediment,
  SerumNOSNotOtherwiseSpecified: v2_0372_SerumNOSNotOtherwiseSpecified,
  Supernatant: v2_0372_Supernatant,
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0373 ( Treatment)
 */
export const V20373 = {
  Acidification: v2_0373_Acidification,
  Alkalization: v2_0373_Alkalization,
  Defibrination: v2_0373_Defibrination,
  Filtration: v2_0373_Filtration,
  LDLPrecipitation: v2_0373_LDLPrecipitation,
  Neutralization: v2_0373_Neutralization,
  Recalification: v2_0373_Recalification,
  Ultrafiltration: v2_0373_Ultrafiltration,
};
const v2_0374_PresentTypeOfContaminationUnspecified: Coding = {
  code: 'CNTM',
  display: 'Present, type of contamination unspecified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0374',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0374 ( System induced contaminants)
 */
export const V20374 = {
  PresentTypeOfContaminationUnspecified: v2_0374_PresentTypeOfContaminationUnspecified,
};
const v2_0375_Fluorocarbons: Coding = {
  code: 'FLUR',
  display: 'Fluorocarbons',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0375',
};
const v2_0375_StromalFreeHemoglobinPreparations: Coding = {
  code: 'SFHB',
  display: 'Stromal free hemoglobin preparations',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0375',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0375 ( Artificial blood)
 */
export const V20375 = {
  Fluorocarbons: v2_0375_Fluorocarbons,
  StromalFreeHemoglobinPreparations: v2_0375_StromalFreeHemoglobinPreparations,
};
const v2_0376_AmbientTemperature: Coding = {
  code: 'AMB',
  display: 'Ambient temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_BodyTemperature: Coding = {
  code: 'C37',
  display: 'Body temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_CriticalAmbientTemperature: Coding = {
  code: 'CAMB',
  display: 'Critical ambient temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_ProtectFromAir: Coding = {
  code: 'CATM',
  display: 'Protect from air',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_CriticalFrozenTemperature: Coding = {
  code: 'CFRZ',
  display: 'Critical frozen temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_CriticalRefrigeratedTemperature: Coding = {
  code: 'CREF',
  display: 'Critical refrigerated temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_DeepFrozen: Coding = {
  code: 'DFRZ',
  display: 'Deep frozen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_Dry: Coding = {
  code: 'DRY',
  display: 'Dry',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_FrozenTemperature: Coding = {
  code: 'FRZ',
  display: 'Frozen temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_MetalFree: Coding = {
  code: 'MTLF',
  display: 'Metal Free',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_LiquidNitrogen: Coding = {
  code: 'NTR',
  display: 'Liquid nitrogen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_ProtectFromLight: Coding = {
  code: 'PRTL',
  display: 'Protect from light',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_DoNotShake: Coding = {
  code: 'PSA',
  display: 'Do not shake',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_NoShock: Coding = {
  code: 'PSO',
  display: 'No shock',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_RefrigeratedTemperature: Coding = {
  code: 'REF',
  display: 'Refrigerated temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_UltraFrozen: Coding = {
  code: 'UFRZ',
  display: 'Ultra frozen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
const v2_0376_Upright: Coding = {
  code: 'UPR',
  display: 'Upright',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0376',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0376 ( Special handling considerations)
 */
export const V20376 = {
  AmbientTemperature: v2_0376_AmbientTemperature,
  BodyTemperature: v2_0376_BodyTemperature,
  CriticalAmbientTemperature: v2_0376_CriticalAmbientTemperature,
  ProtectFromAir: v2_0376_ProtectFromAir,
  CriticalFrozenTemperature: v2_0376_CriticalFrozenTemperature,
  CriticalRefrigeratedTemperature: v2_0376_CriticalRefrigeratedTemperature,
  DeepFrozen: v2_0376_DeepFrozen,
  Dry: v2_0376_Dry,
  FrozenTemperature: v2_0376_FrozenTemperature,
  MetalFree: v2_0376_MetalFree,
  LiquidNitrogen: v2_0376_LiquidNitrogen,
  ProtectFromLight: v2_0376_ProtectFromLight,
  DoNotShake: v2_0376_DoNotShake,
  NoShock: v2_0376_NoShock,
  RefrigeratedTemperature: v2_0376_RefrigeratedTemperature,
  UltraFrozen: v2_0376_UltraFrozen,
  Upright: v2_0376_Upright,
};
const v2_0377_OpenedContainerIndoorAtmosphere60MinutesDuration: Coding = {
  code: 'A60',
  display: 'Opened container, indoor atmosphere, 60 minutes duration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0377',
};
const v2_0377_OpenedContainerAtmosphereAndDurationUnspecified: Coding = {
  code: 'ATM',
  display: 'Opened container, atmosphere and duration unspecified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0377',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0377 ( Other environmental factors)
 */
export const V20377 = {
  OpenedContainerIndoorAtmosphere60MinutesDuration: v2_0377_OpenedContainerIndoorAtmosphere60MinutesDuration,
  OpenedContainerAtmosphereAndDurationUnspecified: v2_0377_OpenedContainerAtmosphereAndDurationUnspecified,
};
const v2_0383_CalibrationError: Coding = {
  code: 'CE',
  display: 'Calibration Error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0383',
};
const v2_0383_CalibrationWarning: Coding = {
  code: 'CW',
  display: 'Calibration Warning',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0383',
};
const v2_0383_ExpiredError: Coding = {
  code: 'EE',
  display: 'Expired Error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0383',
};
const v2_0383_ExpiredWarning: Coding = {
  code: 'EW',
  display: 'Expired Warning',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0383',
};
const v2_0383_NotAvailableError: Coding = {
  code: 'NE',
  display: 'Not Available Error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0383',
};
const v2_0383_NotAvailableWarning: Coding = {
  code: 'NW',
  display: 'Not Available Warning',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0383',
};
const v2_0383_OtherError: Coding = {
  code: 'OE',
  display: 'Other Error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0383',
};
const v2_0383_OKStatus: Coding = {
  code: 'OK',
  display: 'OK Status',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0383',
};
const v2_0383_OtherWarning: Coding = {
  code: 'OW',
  display: 'Other Warning',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0383',
};
const v2_0383_QCError: Coding = {
  code: 'QE',
  display: 'QC Error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0383',
};
const v2_0383_QCWarning: Coding = {
  code: 'QW',
  display: 'QC Warning',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0383',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0383 ( Substance status)
 */
export const V20383 = {
  CalibrationError: v2_0383_CalibrationError,
  CalibrationWarning: v2_0383_CalibrationWarning,
  ExpiredError: v2_0383_ExpiredError,
  ExpiredWarning: v2_0383_ExpiredWarning,
  NotAvailableError: v2_0383_NotAvailableError,
  NotAvailableWarning: v2_0383_NotAvailableWarning,
  OtherError: v2_0383_OtherError,
  OKStatus: v2_0383_OKStatus,
  OtherWarning: v2_0383_OtherWarning,
  QCError: v2_0383_QCError,
  QCWarning: v2_0383_QCWarning,
};
const v2_0384_ControlReagent: Coding = {
  code: 'CO',
  display: 'Control Reagent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0384',
};
const v2_0384_Diluent: Coding = {
  code: 'DI',
  display: 'Diluent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0384',
};
const v2_0384_MeasurableLiquidItem: Coding = {
  code: 'LI',
  display: 'Measurable Liquid Item',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0384',
};
const v2_0384_LiquidWaste: Coding = {
  code: 'LW',
  display: 'Liquid Waste',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0384',
};
const v2_0384_MultipleTestReagent: Coding = {
  code: 'MR',
  display: 'Multiple Test Reagent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0384',
};
const v2_0384_Other: Coding = {
  code: 'OT',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0384',
};
const v2_0384_Pretreatment: Coding = {
  code: 'PT',
  display: 'Pretreatment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0384',
};
const v2_0384_PurifiedWater: Coding = {
  code: 'PW',
  display: 'Purified Water',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0384',
};
const v2_0384_ReagentCalibrator: Coding = {
  code: 'RC',
  display: 'Reagent Calibrator',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0384',
};
const v2_0384_CountableSolidItem: Coding = {
  code: 'SC',
  display: 'Countable Solid Item',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0384',
};
const v2_0384_SingleTestReagent: Coding = {
  code: 'SR',
  display: 'Single Test Reagent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0384',
};
const v2_0384_SolidWaste: Coding = {
  code: 'SW',
  display: 'Solid Waste',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0384',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0384 ( Substance type)
 */
export const V20384 = {
  ControlReagent: v2_0384_ControlReagent,
  Diluent: v2_0384_Diluent,
  MeasurableLiquidItem: v2_0384_MeasurableLiquidItem,
  LiquidWaste: v2_0384_LiquidWaste,
  MultipleTestReagent: v2_0384_MultipleTestReagent,
  Other: v2_0384_Other,
  Pretreatment: v2_0384_Pretreatment,
  PurifiedWater: v2_0384_PurifiedWater,
  ReagentCalibrator: v2_0384_ReagentCalibrator,
  CountableSolidItem: v2_0384_CountableSolidItem,
  SingleTestReagent: v2_0384_SingleTestReagent,
  SolidWaste: v2_0384_SolidWaste,
};
const v2_0387_CommandCannotBeCompletedBecauseOfErrorCondition: Coding = {
  code: 'ER',
  display: 'Command cannot be completed because of error condition',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0387',
};
const v2_0387_CommandCompletedSuccessfully: Coding = {
  code: 'OK',
  display: 'Command completed successfully',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0387',
};
const v2_0387_CommandCannotBeCompletedBecauseOfTheStatusOfTheRequestedEquipment: Coding = {
  code: 'ST',
  display: 'Command cannot be completed because of the status of the requested equipment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0387',
};
const v2_0387_CommandCannotBeCompletedWithinRequestedCompletionTime: Coding = {
  code: 'TI',
  display: 'Command cannot be completed within requested completion time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0387',
};
const v2_0387_CommandCannotBeCompletedForUnknownReasons: Coding = {
  code: 'UN',
  display: 'Command cannot be completed for unknown reasons',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0387',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0387 ( Command response)
 */
export const V20387 = {
  CommandCannotBeCompletedBecauseOfErrorCondition: v2_0387_CommandCannotBeCompletedBecauseOfErrorCondition,
  CommandCompletedSuccessfully: v2_0387_CommandCompletedSuccessfully,
  CommandCannotBeCompletedBecauseOfTheStatusOfTheRequestedEquipment: v2_0387_CommandCannotBeCompletedBecauseOfTheStatusOfTheRequestedEquipment,
  CommandCannotBeCompletedWithinRequestedCompletionTime: v2_0387_CommandCannotBeCompletedWithinRequestedCompletionTime,
  CommandCannotBeCompletedForUnknownReasons: v2_0387_CommandCannotBeCompletedForUnknownReasons,
};
const v2_0388_Evaluation: Coding = {
  code: 'E',
  display: 'Evaluation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0388',
};
const v2_0388_RegularProduction: Coding = {
  code: 'P',
  display: 'Regular Production',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0388',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0388 ( Processing type)
 */
export const V20388 = {
  Evaluation: v2_0388_Evaluation,
  RegularProduction: v2_0388_RegularProduction,
};
const v2_0389_RepeatedWithDilution: Coding = {
  code: 'D',
  display: 'Repeated with dilution',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0389',
};
const v2_0389_ReflexTest: Coding = {
  code: 'F',
  display: 'Reflex test',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0389',
};
const v2_0389_OriginalFirstRun: Coding = {
  code: 'O',
  display: 'Original, first run',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0389',
};
const v2_0389_RepeatedWithoutDilution: Coding = {
  code: 'R',
  display: 'Repeated without dilution',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0389',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0389 ( Analyte repeat status)
 */
export const V20389 = {
  RepeatedWithDilution: v2_0389_RepeatedWithDilution,
  ReflexTest: v2_0389_ReflexTest,
  OriginalFirstRun: v2_0389_OriginalFirstRun,
  RepeatedWithoutDilution: v2_0389_RepeatedWithoutDilution,
};
const v2_0392_MatchOnDateOfBirth: Coding = {
  code: 'DB',
  display: 'Match on Date of Birth',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0392',
};
const v2_0392_MatchOnNameAlphaMatch: Coding = {
  code: 'NA',
  display: 'Match on Name (Alpha Match)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0392',
};
const v2_0392_MatchOnNamePhoneticMatch: Coding = {
  code: 'NP',
  display: 'Match on Name (Phonetic Match)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0392',
};
const v2_0392_MatchOnSocialSecurityNumber: Coding = {
  code: 'SS',
  display: 'Match on Social Security Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0392',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0392 ( Match reason)
 */
export const V20392 = {
  MatchOnDateOfBirth: v2_0392_MatchOnDateOfBirth,
  MatchOnNameAlphaMatch: v2_0392_MatchOnNameAlphaMatch,
  MatchOnNamePhoneticMatch: v2_0392_MatchOnNamePhoneticMatch,
  MatchOnSocialSecurityNumber: v2_0392_MatchOnSocialSecurityNumber,
};
const v2_0393_ProprietaryAlgorithmForLinkSoftV201: Coding = {
  code: 'LINKSOFT_2.01',
  display: 'Proprietary algorithm for LinkSoft v2.01',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0393',
};
const v2_0393_ProprietaryAlgorithmForMatchWareV12: Coding = {
  code: 'MATCHWARE_1.2',
  display: 'Proprietary algorithm for MatchWare v1.2',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0393',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0393 ( Match algorithms)
 */
export const V20393 = {
  ProprietaryAlgorithmForLinkSoftV201: v2_0393_ProprietaryAlgorithmForLinkSoftV201,
  ProprietaryAlgorithmForMatchWareV12: v2_0393_ProprietaryAlgorithmForMatchWareV12,
};
const v2_0394_Batch: Coding = {
  code: 'B',
  display: 'Batch',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0394',
};
const v2_0394_RealTime: Coding = {
  code: 'R',
  display: 'Real Time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0394',
};
const v2_0394_BolusASeriesOfResponsesSentAtTheSameTimeWithoutUseOfBatchFormatting: Coding = {
  code: 'T',
  display: 'Bolus (a series of responses sent at the same time without use of batch formatting)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0394',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0394 ( Response modality)
 */
export const V20394 = {
  Batch: v2_0394_Batch,
  RealTime: v2_0394_RealTime,
  BolusASeriesOfResponsesSentAtTheSameTimeWithoutUseOfBatchFormatting: v2_0394_BolusASeriesOfResponsesSentAtTheSameTimeWithoutUseOfBatchFormatting,
};
const v2_0395_ModifiedSubscription: Coding = {
  code: 'M',
  display: 'Modified Subscription',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0395',
};
const v2_0395_NewSubscription: Coding = {
  code: 'N',
  display: 'New Subscription',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0395',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0395 ( Modify indicator)
 */
export const V20395 = {
  ModifiedSubscription: v2_0395_ModifiedSubscription,
  NewSubscription: v2_0395_NewSubscription,
};
const v2_0397_Ascending: Coding = {
  code: 'A',
  display: 'Ascending',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0397',
};
const v2_0397_AscendingCaseInsensitive: Coding = {
  code: 'AN',
  display: 'Ascending, case insensitive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0397',
};
const v2_0397_Descending: Coding = {
  code: 'D',
  display: 'Descending',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0397',
};
const v2_0397_DescendingCaseInsensitive: Coding = {
  code: 'DN',
  display: 'Descending, case insensitive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0397',
};
const v2_0397_None: Coding = {
  code: 'N',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0397',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0397 ( Sequencing)
 */
export const V20397 = {
  Ascending: v2_0397_Ascending,
  AscendingCaseInsensitive: v2_0397_AscendingCaseInsensitive,
  Descending: v2_0397_Descending,
  DescendingCaseInsensitive: v2_0397_DescendingCaseInsensitive,
  None: v2_0397_None,
};
const v2_0398_Fragmentation: Coding = {
  code: 'F',
  display: 'Fragmentation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0398',
};
const v2_0398_InteractiveContinuation: Coding = {
  code: 'I',
  display: 'Interactive Continuation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0398',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0398 ( Continuation style code)
 */
export const V20398 = {
  Fragmentation: v2_0398_Fragmentation,
  InteractiveContinuation: v2_0398_InteractiveContinuation,
};
const v2_0401_MediCal: Coding = {
  code: 'C',
  display: 'Medi-Cal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0401',
};
const v2_0401_Medicare: Coding = {
  code: 'MM',
  display: 'Medicare',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0401',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0401 ( Government reimbursement program)
 */
export const V20401 = {
  MediCal: v2_0401_MediCal,
  Medicare: v2_0401_Medicare,
};
const v2_0402_Dental: Coding = {
  code: 'D',
  display: 'Dental',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0402',
};
const v2_0402_Graduate: Coding = {
  code: 'G',
  display: 'Graduate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0402',
};
const v2_0402_Medical: Coding = {
  code: 'M',
  display: 'Medical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0402',
};
const v2_0402_Undergraduate: Coding = {
  code: 'U',
  display: 'Undergraduate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0402',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0402 ( School type)
 */
export const V20402 = {
  Dental: v2_0402_Dental,
  Graduate: v2_0402_Graduate,
  Medical: v2_0402_Medical,
  Undergraduate: v2_0402_Undergraduate,
};
const v2_0403_Read: Coding = {
  code: '1',
  display: 'Read',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0403',
};
const v2_0403_Write: Coding = {
  code: '2',
  display: 'Write',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0403',
};
const v2_0403_Speak: Coding = {
  code: '3',
  display: 'Speak',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0403',
};
const v2_0403_Understand: Coding = {
  code: '4',
  display: 'Understand',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0403',
};
const v2_0403_Sign: Coding = {
  code: '5',
  display: 'Sign',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0403',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0403 ( Language ability)
 */
export const V20403 = {
  Read: v2_0403_Read,
  Write: v2_0403_Write,
  Speak: v2_0403_Speak,
  Understand: v2_0403_Understand,
  Sign: v2_0403_Sign,
};
const v2_0404_Excellent: Coding = {
  code: '1',
  display: 'Excellent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0404',
};
const v2_0404_Good: Coding = {
  code: '2',
  display: 'Good',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0404',
};
const v2_0404_Fair: Coding = {
  code: '3',
  display: 'Fair',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0404',
};
const v2_0404_Poor: Coding = {
  code: '4',
  display: 'Poor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0404',
};
const v2_0404_SomeLevelUnknown: Coding = {
  code: '5',
  display: 'Some (level unknown)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0404',
};
const v2_0404_None: Coding = {
  code: '6',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0404',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0404 ( Language proficiency)
 */
export const V20404 = {
  Excellent: v2_0404_Excellent,
  Good: v2_0404_Good,
  Fair: v2_0404_Fair,
  Poor: v2_0404_Poor,
  SomeLevelUnknown: v2_0404_SomeLevelUnknown,
  None: v2_0404_None,
};
const v2_0406_Hospital: Coding = {
  code: '1',
  display: 'Hospital',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0406',
};
const v2_0406_PhysicianClinic: Coding = {
  code: '2',
  display: 'Physician Clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0406',
};
const v2_0406_LongTermCare: Coding = {
  code: '3',
  display: 'Long Term Care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0406',
};
const v2_0406_AcuteCare: Coding = {
  code: '4',
  display: 'Acute Care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0406',
};
const v2_0406_Other: Coding = {
  code: '5',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0406',
};
const v2_0406_Home: Coding = {
  code: 'H',
  display: 'Home',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0406',
};
const v2_0406_Office: Coding = {
  code: 'O',
  display: 'Office',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0406',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0406 ( Participant Organization Unit Type)
 */
export const V20406 = {
  Hospital: v2_0406_Hospital,
  PhysicianClinic: v2_0406_PhysicianClinic,
  LongTermCare: v2_0406_LongTermCare,
  AcuteCare: v2_0406_AcuteCare,
  Other: v2_0406_Other,
  Home: v2_0406_Home,
  Office: v2_0406_Office,
};
const v2_0409_MigratesToDifferentCPU: Coding = {
  code: 'M',
  display: 'Migrates to different CPU',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0409',
};
const v2_0409_ShutDown: Coding = {
  code: 'SD',
  display: 'Shut down',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0409',
};
const v2_0409_StartUp: Coding = {
  code: 'SU',
  display: 'Start up',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0409',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0409 ( Application change type)
 */
export const V20409 = {
  MigratesToDifferentCPU: v2_0409_MigratesToDifferentCPU,
  ShutDown: v2_0409_ShutDown,
  StartUp: v2_0409_StartUp,
};
const v2_0411_ObservationOfTypeClinicalGenomicsCG: Coding = {
  code: 'CG',
  display: 'Observation of type Clinical Genomics (CG)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0411',
};
const v2_0411_ObservationOfTypeNewbornDriedBloodScreeningNDBS: Coding = {
  code: 'NDBS',
  display: 'Observation of type Newborn Dried Blood Screening (NDBS)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0411',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0411 ( Supplemental service information values)
 */
export const V20411 = {
  ObservationOfTypeClinicalGenomicsCG: v2_0411_ObservationOfTypeClinicalGenomicsCG,
  ObservationOfTypeNewbornDriedBloodScreeningNDBS: v2_0411_ObservationOfTypeNewbornDriedBloodScreeningNDBS,
};
const v2_0415_DRGExempt: Coding = {
  code: 'E',
  display: 'DRG Exempt',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0415',
};
const v2_0415_DRGNonExempt: Coding = {
  code: 'N',
  display: 'DRG Non Exempt',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0415',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0415 ( DRG transfer type)
 */
export const V20415 = {
  DRGExempt: v2_0415_DRGExempt,
  DRGNonExempt: v2_0415_DRGNonExempt,
};
const v2_0416_VAL1stNonOperative: Coding = {
  code: '1',
  display: '1st non-Operative',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0416',
};
const v2_0416_VAL2ndNonOperative: Coding = {
  code: '2',
  display: '2nd non-Operative',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0416',
};
const v2_0416_MajorOperative: Coding = {
  code: '3',
  display: 'Major Operative',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0416',
};
const v2_0416_VAL2ndOperative: Coding = {
  code: '4',
  display: '2nd Operative',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0416',
};
const v2_0416_VAL3rdOperative: Coding = {
  code: '5',
  display: '3rd Operative',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0416',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0416 ( Procedure DRG type)
 */
export const V20416 = {
  VAL1stNonOperative: v2_0416_VAL1stNonOperative,
  VAL2ndNonOperative: v2_0416_VAL2ndNonOperative,
  MajorOperative: v2_0416_MajorOperative,
  VAL2ndOperative: v2_0416_VAL2ndOperative,
  VAL3rdOperative: v2_0416_VAL3rdOperative,
};
const v2_0417_NoTissueExpected: Coding = {
  code: '0',
  display: 'No tissue expected',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0417',
};
const v2_0417_InsufficientTissue: Coding = {
  code: '1',
  display: 'Insufficient Tissue',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0417',
};
const v2_0417_NotAbnormal: Coding = {
  code: '2',
  display: 'Not abnormal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0417',
};
const v2_0417_AbnormalNotCategorized: Coding = {
  code: '3',
  display: 'Abnormal-not categorized',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0417',
};
const v2_0417_MechanicalAbnormal: Coding = {
  code: '4',
  display: 'Mechanical abnormal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0417',
};
const v2_0417_GrowthAlteration: Coding = {
  code: '5',
  display: 'Growth alteration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0417',
};
const v2_0417_DegenerationAndNecrosis: Coding = {
  code: '6',
  display: 'Degeneration & necrosis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0417',
};
const v2_0417_NonAcuteInflammation: Coding = {
  code: '7',
  display: 'Non-acute inflammation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0417',
};
const v2_0417_NonMalignantNeoplasm: Coding = {
  code: '8',
  display: 'Non-malignant neoplasm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0417',
};
const v2_0417_MalignantNeoplasm: Coding = {
  code: '9',
  display: 'Malignant neoplasm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0417',
};
const v2_0417_BasalCellCarcinoma: Coding = {
  code: 'B',
  display: 'Basal cell carcinoma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0417',
};
const v2_0417_CarcinomaUnspecifiedType: Coding = {
  code: 'C',
  display: 'Carcinoma-unspecified type',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0417',
};
const v2_0417_AdditionalTissueRequired: Coding = {
  code: 'G',
  display: 'Additional tissue required',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0417',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0417 ( Tissue type code)
 */
export const V20417 = {
  NoTissueExpected: v2_0417_NoTissueExpected,
  InsufficientTissue: v2_0417_InsufficientTissue,
  NotAbnormal: v2_0417_NotAbnormal,
  AbnormalNotCategorized: v2_0417_AbnormalNotCategorized,
  MechanicalAbnormal: v2_0417_MechanicalAbnormal,
  GrowthAlteration: v2_0417_GrowthAlteration,
  DegenerationAndNecrosis: v2_0417_DegenerationAndNecrosis,
  NonAcuteInflammation: v2_0417_NonAcuteInflammation,
  NonMalignantNeoplasm: v2_0417_NonMalignantNeoplasm,
  MalignantNeoplasm: v2_0417_MalignantNeoplasm,
  BasalCellCarcinoma: v2_0417_BasalCellCarcinoma,
  CarcinomaUnspecifiedType: v2_0417_CarcinomaUnspecifiedType,
  AdditionalTissueRequired: v2_0417_AdditionalTissueRequired,
};
const v2_0418_NoSuggestedValuesDefined: Coding = {
  code: '...',
  display: 'No suggested values defined',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0418',
};
const v2_0418_TheAdmittingProcedure: Coding = {
  code: '0',
  display: 'the admitting procedure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0418',
};
const v2_0418_ThePrimaryProcedure: Coding = {
  code: '1',
  display: 'the primary procedure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0418',
};
const v2_0418_ForRankedSecondaryProcedures: Coding = {
  code: '2',
  display: 'for ranked secondary procedures',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0418',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0418 ( Procedure priority)
 */
export const V20418 = {
  NoSuggestedValuesDefined: v2_0418_NoSuggestedValuesDefined,
  TheAdmittingProcedure: v2_0418_TheAdmittingProcedure,
  ThePrimaryProcedure: v2_0418_ThePrimaryProcedure,
  ForRankedSecondaryProcedures: v2_0418_ForRankedSecondaryProcedures,
};
const v2_0421_Mild: Coding = {
  code: 'MI',
  display: 'Mild',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0421',
};
const v2_0421_Moderate: Coding = {
  code: 'MO',
  display: 'Moderate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0421',
};
const v2_0421_Severe: Coding = {
  code: 'SE',
  display: 'Severe',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0421',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0421 ( Severity of illness code)
 */
export const V20421 = {
  Mild: v2_0421_Mild,
  Moderate: v2_0421_Moderate,
  Severe: v2_0421_Severe,
};
const v2_0422_NonAcute: Coding = {
  code: '1',
  display: 'Non-acute',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0422',
};
const v2_0422_Acute: Coding = {
  code: '2',
  display: 'Acute',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0422',
};
const v2_0422_Urgent: Coding = {
  code: '3',
  display: 'Urgent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0422',
};
const v2_0422_Severe: Coding = {
  code: '4',
  display: 'Severe',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0422',
};
const v2_0422_DeadOnArrivalDOA: Coding = {
  code: '5',
  display: 'Dead on Arrival (DOA)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0422',
};
const v2_0422_Other: Coding = {
  code: '99',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0422',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0422 ( Triage code)
 */
export const V20422 = {
  NonAcute: v2_0422_NonAcute,
  Acute: v2_0422_Acute,
  Urgent: v2_0422_Urgent,
  Severe: v2_0422_Severe,
  DeadOnArrivalDOA: v2_0422_DeadOnArrivalDOA,
  Other: v2_0422_Other,
};
const v2_0423_DoctorQuoteSOfficeClosed: Coding = {
  code: 'D',
  display: "Doctor's Office Closed",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0423',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0423 ( Case category code)
 */
export const V20423 = {
  DoctorQuoteSOfficeClosed: v2_0423_DoctorQuoteSOfficeClosed,
};
const v2_0424_PrematurePreTerm: Coding = {
  code: '1',
  display: 'Premature / Pre-term',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0424',
};
const v2_0424_FullTerm: Coding = {
  code: '2',
  display: 'Full Term',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0424',
};
const v2_0424_OverduePostTerm: Coding = {
  code: '3',
  display: 'Overdue / Post-term',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0424',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0424 ( Gestation category code)
 */
export const V20424 = {
  PrematurePreTerm: v2_0424_PrematurePreTerm,
  FullTerm: v2_0424_FullTerm,
  OverduePostTerm: v2_0424_OverduePostTerm,
};
const v2_0425_BornInFacility: Coding = {
  code: '1',
  display: 'Born in facility',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0425',
};
const v2_0425_TransferIn: Coding = {
  code: '2',
  display: 'Transfer in',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0425',
};
const v2_0425_BornEnRoute: Coding = {
  code: '3',
  display: 'Born en route',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0425',
};
const v2_0425_Other: Coding = {
  code: '4',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0425',
};
const v2_0425_BornAtHome: Coding = {
  code: '5',
  display: 'Born at home',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0425',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0425 ( Newborn code)
 */
export const V20425 = {
  BornInFacility: v2_0425_BornInFacility,
  TransferIn: v2_0425_TransferIn,
  BornEnRoute: v2_0425_BornEnRoute,
  Other: v2_0425_Other,
  BornAtHome: v2_0425_BornAtHome,
};
const v2_0426_CryoprecipitatedAHF: Coding = {
  code: 'CRYO',
  display: 'Cryoprecipitated AHF',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_PooledCryoprecipitate: Coding = {
  code: 'CRYOP',
  display: 'Pooled Cryoprecipitate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_FreshFrozenPlasma: Coding = {
  code: 'FFP',
  display: 'Fresh Frozen Plasma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_FreshFrozenPlasmaThawed: Coding = {
  code: 'FFPTH',
  display: 'Fresh Frozen Plasma - Thawed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_PackedCells: Coding = {
  code: 'PC',
  display: 'Packed Cells',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_AutologousPackedCells: Coding = {
  code: 'PCA',
  display: 'Autologous Packed Cells',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_PackedCellsNeonatal: Coding = {
  code: 'PCNEO',
  display: 'Packed Cells - Neonatal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_WashedPackedCells: Coding = {
  code: 'PCW',
  display: 'Washed Packed Cells',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_PlateletConcentrate: Coding = {
  code: 'PLT',
  display: 'Platelet Concentrate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_ReducedVolumePlatelets: Coding = {
  code: 'PLTNEO',
  display: 'Reduced Volume Platelets',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_PooledPlatelets: Coding = {
  code: 'PLTP',
  display: 'Pooled Platelets',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_PlateletPheresis: Coding = {
  code: 'PLTPH',
  display: 'Platelet Pheresis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_LeukoreducedPlateletPheresis: Coding = {
  code: 'PLTPHLR',
  display: 'Leukoreduced Platelet Pheresis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_ReconstitutedWholeBlood: Coding = {
  code: 'RWB',
  display: 'Reconstituted Whole Blood',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
const v2_0426_AutologousWholeBlood: Coding = {
  code: 'WBA',
  display: 'Autologous Whole Blood',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0426',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0426 ( Blood product code)
 */
export const V20426 = {
  CryoprecipitatedAHF: v2_0426_CryoprecipitatedAHF,
  PooledCryoprecipitate: v2_0426_PooledCryoprecipitate,
  FreshFrozenPlasma: v2_0426_FreshFrozenPlasma,
  FreshFrozenPlasmaThawed: v2_0426_FreshFrozenPlasmaThawed,
  PackedCells: v2_0426_PackedCells,
  AutologousPackedCells: v2_0426_AutologousPackedCells,
  PackedCellsNeonatal: v2_0426_PackedCellsNeonatal,
  WashedPackedCells: v2_0426_WashedPackedCells,
  PlateletConcentrate: v2_0426_PlateletConcentrate,
  ReducedVolumePlatelets: v2_0426_ReducedVolumePlatelets,
  PooledPlatelets: v2_0426_PooledPlatelets,
  PlateletPheresis: v2_0426_PlateletPheresis,
  LeukoreducedPlateletPheresis: v2_0426_LeukoreducedPlateletPheresis,
  ReconstitutedWholeBlood: v2_0426_ReconstitutedWholeBlood,
  AutologousWholeBlood: v2_0426_AutologousWholeBlood,
};
const v2_0427_BodyFluidExposure: Coding = {
  code: 'B',
  display: 'Body fluid exposure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
const v2_0427_ContaminatedSubstance: Coding = {
  code: 'C',
  display: 'Contaminated Substance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
const v2_0427_DietErrors: Coding = {
  code: 'D',
  display: 'Diet Errors',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
const v2_0427_EquipmentProblem: Coding = {
  code: 'E',
  display: 'Equipment problem',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
const v2_0427_PatientFellNotFromBed: Coding = {
  code: 'F',
  display: 'Patient fell (not from bed)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
const v2_0427_PatientFellFromBed: Coding = {
  code: 'H',
  display: 'Patient fell from bed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
const v2_0427_InfusionError: Coding = {
  code: 'I',
  display: 'Infusion error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
const v2_0427_ForeignObjectLeftDuringSurgery: Coding = {
  code: 'J',
  display: 'Foreign object left during surgery',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
const v2_0427_SterilePrecautionViolated: Coding = {
  code: 'K',
  display: 'Sterile precaution violated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
const v2_0427_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
const v2_0427_ProcedureError: Coding = {
  code: 'P',
  display: 'Procedure error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
const v2_0427_PharmaceuticalError: Coding = {
  code: 'R',
  display: 'Pharmaceutical error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
const v2_0427_SuicideAttempt: Coding = {
  code: 'S',
  display: 'Suicide Attempt',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
const v2_0427_TransfusionError: Coding = {
  code: 'T',
  display: 'Transfusion error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0427',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0427 ( Risk management incident code)
 */
export const V20427 = {
  BodyFluidExposure: v2_0427_BodyFluidExposure,
  ContaminatedSubstance: v2_0427_ContaminatedSubstance,
  DietErrors: v2_0427_DietErrors,
  EquipmentProblem: v2_0427_EquipmentProblem,
  PatientFellNotFromBed: v2_0427_PatientFellNotFromBed,
  PatientFellFromBed: v2_0427_PatientFellFromBed,
  InfusionError: v2_0427_InfusionError,
  ForeignObjectLeftDuringSurgery: v2_0427_ForeignObjectLeftDuringSurgery,
  SterilePrecautionViolated: v2_0427_SterilePrecautionViolated,
  Other: v2_0427_Other,
  ProcedureError: v2_0427_ProcedureError,
  PharmaceuticalError: v2_0427_PharmaceuticalError,
  SuicideAttempt: v2_0427_SuicideAttempt,
  TransfusionError: v2_0427_TransfusionError,
};
const v2_0428_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0428',
};
const v2_0428_Preventable: Coding = {
  code: 'P',
  display: 'Preventable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0428',
};
const v2_0428_UserError: Coding = {
  code: 'U',
  display: 'User Error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0428',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0428 ( Incident type code)
 */
export const V20428 = {
  Other: v2_0428_Other,
  Preventable: v2_0428_Preventable,
  UserError: v2_0428_UserError,
};
const v2_0429_BreedingGeneticStock: Coding = {
  code: 'BR',
  display: 'Breeding/genetic stock',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0429',
};
const v2_0429_Dairy: Coding = {
  code: 'DA',
  display: 'Dairy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0429',
};
const v2_0429_Draft: Coding = {
  code: 'DR',
  display: 'Draft',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0429',
};
const v2_0429_DualPurpose: Coding = {
  code: 'DU',
  display: 'Dual Purpose',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0429',
};
const v2_0429_LayerIncludesMultiplierFlocks: Coding = {
  code: 'LY',
  display: 'Layer, Includes Multiplier flocks',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0429',
};
const v2_0429_Meat: Coding = {
  code: 'MT',
  display: 'Meat',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0429',
};
const v2_0429_NotApplicable: Coding = {
  code: 'NA',
  display: 'Not Applicable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0429',
};
const v2_0429_Other: Coding = {
  code: 'OT',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0429',
};
const v2_0429_Pleasure: Coding = {
  code: 'PL',
  display: 'Pleasure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0429',
};
const v2_0429_Racing: Coding = {
  code: 'RA',
  display: 'Racing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0429',
};
const v2_0429_Show: Coding = {
  code: 'SH',
  display: 'Show',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0429',
};
const v2_0429_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0429',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0429 ( Production class Code)
 */
export const V20429 = {
  BreedingGeneticStock: v2_0429_BreedingGeneticStock,
  Dairy: v2_0429_Dairy,
  Draft: v2_0429_Draft,
  DualPurpose: v2_0429_DualPurpose,
  LayerIncludesMultiplierFlocks: v2_0429_LayerIncludesMultiplierFlocks,
  Meat: v2_0429_Meat,
  NotApplicable: v2_0429_NotApplicable,
  Other: v2_0429_Other,
  Pleasure: v2_0429_Pleasure,
  Racing: v2_0429_Racing,
  Show: v2_0429_Show,
  Unknown: v2_0429_Unknown,
};
const v2_0430_Ambulance: Coding = {
  code: 'A',
  display: 'Ambulance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0430',
};
const v2_0430_Car: Coding = {
  code: 'C',
  display: 'Car',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0430',
};
const v2_0430_OnFoot: Coding = {
  code: 'F',
  display: 'On foot',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0430',
};
const v2_0430_Helicopter: Coding = {
  code: 'H',
  display: 'Helicopter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0430',
};
const v2_0430_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0430',
};
const v2_0430_PublicTransport: Coding = {
  code: 'P',
  display: 'Public Transport',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0430',
};
const v2_0430_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0430',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0430 ( Mode of arrival code)
 */
export const V20430 = {
  Ambulance: v2_0430_Ambulance,
  Car: v2_0430_Car,
  OnFoot: v2_0430_OnFoot,
  Helicopter: v2_0430_Helicopter,
  Other: v2_0430_Other,
  PublicTransport: v2_0430_PublicTransport,
  Unknown: v2_0430_Unknown,
};
const v2_0431_Alcohol: Coding = {
  code: 'A',
  display: 'Alcohol',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0431',
};
const v2_0431_TobaccoChewed: Coding = {
  code: 'C',
  display: 'Tobacco - chewed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0431',
};
const v2_0431_Kava: Coding = {
  code: 'K',
  display: 'Kava',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0431',
};
const v2_0431_Marijuana: Coding = {
  code: 'M',
  display: 'Marijuana',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0431',
};
const v2_0431_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0431',
};
const v2_0431_TobaccoSmoked: Coding = {
  code: 'T',
  display: 'Tobacco - smoked',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0431',
};
const v2_0431_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0431',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0431 ( Recreational drug use code)
 */
export const V20431 = {
  Alcohol: v2_0431_Alcohol,
  TobaccoChewed: v2_0431_TobaccoChewed,
  Kava: v2_0431_Kava,
  Marijuana: v2_0431_Marijuana,
  Other: v2_0431_Other,
  TobaccoSmoked: v2_0431_TobaccoSmoked,
  Unknown: v2_0431_Unknown,
};
const v2_0432_Acute: Coding = {
  code: 'AC',
  display: 'Acute',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0432',
};
const v2_0432_Chronic: Coding = {
  code: 'CH',
  display: 'Chronic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0432',
};
const v2_0432_Comatose: Coding = {
  code: 'CO',
  display: 'Comatose',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0432',
};
const v2_0432_Critical: Coding = {
  code: 'CR',
  display: 'Critical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0432',
};
const v2_0432_Improved: Coding = {
  code: 'IM',
  display: 'Improved',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0432',
};
const v2_0432_Moribund: Coding = {
  code: 'MO',
  display: 'Moribund',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0432',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0432 ( Admission level of care code)
 */
export const V20432 = {
  Acute: v2_0432_Acute,
  Chronic: v2_0432_Chronic,
  Comatose: v2_0432_Comatose,
  Critical: v2_0432_Critical,
  Improved: v2_0432_Improved,
  Moribund: v2_0432_Moribund,
};
const v2_0433_Aggressive: Coding = {
  code: 'A',
  display: 'Aggressive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0433',
};
const v2_0433_Blind: Coding = {
  code: 'B',
  display: 'Blind',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0433',
};
const v2_0433_Confused: Coding = {
  code: 'C',
  display: 'Confused',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0433',
};
const v2_0433_Deaf: Coding = {
  code: 'D',
  display: 'Deaf',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0433',
};
const v2_0433_OnIV: Coding = {
  code: 'I',
  display: 'On IV',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0433',
};
const v2_0433_DoNotResuscitate: Coding = {
  code: 'N',
  display: 'Do not resuscitate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0433',
};
const v2_0433_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0433',
};
const v2_0433_Paraplegic: Coding = {
  code: 'P',
  display: 'Paraplegic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0433',
};
const v2_0433_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0433',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0433 ( Precaution code)
 */
export const V20433 = {
  Aggressive: v2_0433_Aggressive,
  Blind: v2_0433_Blind,
  Confused: v2_0433_Confused,
  Deaf: v2_0433_Deaf,
  OnIV: v2_0433_OnIV,
  DoNotResuscitate: v2_0433_DoNotResuscitate,
  Other: v2_0433_Other,
  Paraplegic: v2_0433_Paraplegic,
  Unknown: v2_0433_Unknown,
};
const v2_0434_Satisfactory: Coding = {
  code: 'A',
  display: 'Satisfactory',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0434',
};
const v2_0434_Critical: Coding = {
  code: 'C',
  display: 'Critical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0434',
};
const v2_0434_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0434',
};
const v2_0434_Poor: Coding = {
  code: 'P',
  display: 'Poor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0434',
};
const v2_0434_Stable: Coding = {
  code: 'S',
  display: 'Stable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0434',
};
const v2_0434_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0434',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0434 ( Patient condition code)
 */
export const V20434 = {
  Satisfactory: v2_0434_Satisfactory,
  Critical: v2_0434_Critical,
  Other: v2_0434_Other,
  Poor: v2_0434_Poor,
  Stable: v2_0434_Stable,
  Unknown: v2_0434_Unknown,
};
const v2_0435_DoNotResuscitate: Coding = {
  code: 'DNR',
  display: 'Do not resuscitate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0435',
};
const v2_0435_NoDirective: Coding = {
  code: 'N',
  display: 'No directive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0435',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0435 ( Advance directive code)
 */
export const V20435 = {
  DoNotResuscitate: v2_0435_DoNotResuscitate,
  NoDirective: v2_0435_NoDirective,
};
const v2_0436_AdverseReactionNotOtherwiseClassified: Coding = {
  code: 'AD',
  display: 'Adverse Reaction (Not otherwise classified)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0436',
};
const v2_0436_Allergy: Coding = {
  code: 'AL',
  display: 'Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0436',
};
const v2_0436_Contraindication: Coding = {
  code: 'CT',
  display: 'Contraindication',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0436',
};
const v2_0436_Intolerance: Coding = {
  code: 'IN',
  display: 'Intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0436',
};
const v2_0436_SideEffect: Coding = {
  code: 'SE',
  display: 'Side Effect',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0436',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0436 ( Sensitivity to Causative Agent code)
 */
export const V20436 = {
  AdverseReactionNotOtherwiseClassified: v2_0436_AdverseReactionNotOtherwiseClassified,
  Allergy: v2_0436_Allergy,
  Contraindication: v2_0436_Contraindication,
  Intolerance: v2_0436_Intolerance,
  SideEffect: v2_0436_SideEffect,
};
const v2_0437_Bracelet: Coding = {
  code: 'B',
  display: 'Bracelet',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0437',
};
const v2_0437_Necklace: Coding = {
  code: 'N',
  display: 'Necklace',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0437',
};
const v2_0437_WalletCard: Coding = {
  code: 'W',
  display: 'Wallet Card',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0437',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0437 ( Alert device code)
 */
export const V20437 = {
  Bracelet: v2_0437_Bracelet,
  Necklace: v2_0437_Necklace,
  WalletCard: v2_0437_WalletCard,
};
const v2_0438_ConfirmedOrVerified: Coding = {
  code: 'C',
  display: 'Confirmed or verified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0438',
};
const v2_0438_DoubtRaised: Coding = {
  code: 'D',
  display: 'Doubt raised',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0438',
};
const v2_0438_Erroneous: Coding = {
  code: 'E',
  display: 'Erroneous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0438',
};
const v2_0438_ConfirmedButInactive: Coding = {
  code: 'I',
  display: 'Confirmed but inactive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0438',
};
const v2_0438_Pending: Coding = {
  code: 'P',
  display: 'Pending',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0438',
};
const v2_0438_Suspect: Coding = {
  code: 'S',
  display: 'Suspect',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0438',
};
const v2_0438_Unconfirmed: Coding = {
  code: 'U',
  display: 'Unconfirmed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0438',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0438 ( Allergy clinical status)
 */
export const V20438 = {
  ConfirmedOrVerified: v2_0438_ConfirmedOrVerified,
  DoubtRaised: v2_0438_DoubtRaised,
  Erroneous: v2_0438_Erroneous,
  ConfirmedButInactive: v2_0438_ConfirmedButInactive,
  Pending: v2_0438_Pending,
  Suspect: v2_0438_Suspect,
  Unconfirmed: v2_0438_Unconfirmed,
};
const v2_0440_ErrorLocationAndDescription: Coding = {
  code: 'ELD',
  display: 'Error location and description',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_FamilyName: Coding = {
  code: 'FN',
  display: 'Family name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_OrderSequenceDefinition: Coding = {
  code: 'OSD',
  display: 'Order sequence definition',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_StreetAddress: Coding = {
  code: 'SAD',
  display: 'Street Address',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
const v2_0440_SpecimenSource: Coding = {
  code: 'SPS',
  display: 'Specimen source',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0440',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0440 ( Data types)
 */
export const V20440 = {
  Address: v2_0440_Address,
  AuthorizationInformation: v2_0440_AuthorizationInformation,
  ChargeCodeAndDate: v2_0440_ChargeCodeAndDate,
  ChannelCalibrationParameters: v2_0440_ChannelCalibrationParameters,
  ChannelDefinition: v2_0440_ChannelDefinition,
  CodedElement: v2_0440_CodedElement,
  CodedElementWithFormattedValues: v2_0440_CodedElementWithFormattedValues,
  CompositeIDWithCheckDigit: v2_0440_CompositeIDWithCheckDigit,
  Composite: v2_0440_Composite,
  CompositeIDNumberAndName: v2_0440_CompositeIDNumberAndName,
  CodedWithNoExceptions: v2_0440_CodedWithNoExceptions,
  CompositeIDNumberAndNameSimplified: v2_0440_CompositeIDNumberAndNameSimplified,
  CompositePrice: v2_0440_CompositePrice,
  CompositeQuantityWithUnits: v2_0440_CompositeQuantityWithUnits,
  ChannelSensitivityAndUnits: v2_0440_ChannelSensitivityAndUnits,
  CodedWithExceptions: v2_0440_CodedWithExceptions,
  ExtendedCompositeIDWithCheckDigit: v2_0440_ExtendedCompositeIDWithCheckDigit,
  DailyDeductibleInformation: v2_0440_DailyDeductibleInformation,
  DateAndInstitutionName: v2_0440_DateAndInstitutionName,
  DischargeToLocationAndDate: v2_0440_DischargeToLocationAndDate,
  DriverQuoteSLicenseNumber: v2_0440_DriverQuoteSLicenseNumber,
  Delta: v2_0440_Delta,
  DateTimeRange: v2_0440_DateTimeRange,
  Date: v2_0440_Date,
  DateTime: v2_0440_DateTime,
  DayTypeAndNumber: v2_0440_DayTypeAndNumber,
  EncapsulatedData: v2_0440_EncapsulatedData,
  EntityIdentifier: v2_0440_EntityIdentifier,
  EntityIdentifierPair: v2_0440_EntityIdentifierPair,
  ErrorLocationAndDescription: v2_0440_ErrorLocationAndDescription,
  ErrorLocation: v2_0440_ErrorLocation,
  FinancialClass: v2_0440_FinancialClass,
  FamilyName: v2_0440_FamilyName,
  FormattedText: v2_0440_FormattedText,
  GeneralTimingSpecification: v2_0440_GeneralTimingSpecification,
  HierarchicDesignator: v2_0440_HierarchicDesignator,
  InsuranceCertificationDefinition: v2_0440_InsuranceCertificationDefinition,
  CodedValuesForHL7Tables: v2_0440_CodedValuesForHL7Tables,
  CodedValueForUserDefinedTables: v2_0440_CodedValueForUserDefinedTables,
  JobCodeClass: v2_0440_JobCodeClass,
  LocationWithAddressVariation1: v2_0440_LocationWithAddressVariation1,
  LocationWithAddressVariation2: v2_0440_LocationWithAddressVariation2,
  MultiplexedArray: v2_0440_MultiplexedArray,
  Money: v2_0440_Money,
  MoneyAndChargeCode: v2_0440_MoneyAndChargeCode,
  MoneyOrPercentage: v2_0440_MoneyOrPercentage,
  MessageType: v2_0440_MessageType,
  NumericArray: v2_0440_NumericArray,
  NameWithDateAndLocation: v2_0440_NameWithDateAndLocation,
  Numeric: v2_0440_Numeric,
  NumericRange: v2_0440_NumericRange,
  OccurrenceCodeAndDate: v2_0440_OccurrenceCodeAndDate,
  OrderSequenceDefinition: v2_0440_OrderSequenceDefinition,
  OccurrenceSpanCodeAndDate: v2_0440_OccurrenceSpanCodeAndDate,
  PractitionerInstitutionalPrivileges: v2_0440_PractitionerInstitutionalPrivileges,
  PersonLocation: v2_0440_PersonLocation,
  PractitionerLicenseOrOtherIDNumber: v2_0440_PractitionerLicenseOrOtherIDNumber,
  PersonName: v2_0440_PersonName,
  PerformingPersonTimeStamp: v2_0440_PerformingPersonTimeStamp,
  ParentResultLink: v2_0440_ParentResultLink,
  ProcessingType: v2_0440_ProcessingType,
  PolicyTypeAndAmount: v2_0440_PolicyTypeAndAmount,
  QueryInputParameterList: v2_0440_QueryInputParameterList,
  QuerySelectionCriteria: v2_0440_QuerySelectionCriteria,
  RowColumnDefinition: v2_0440_RowColumnDefinition,
  ReferenceRange: v2_0440_ReferenceRange,
  RepeatInterval: v2_0440_RepeatInterval,
  RoomCoverage: v2_0440_RoomCoverage,
  ReferencePointer: v2_0440_ReferencePointer,
  RepeatPattern: v2_0440_RepeatPattern,
  StreetAddress: v2_0440_StreetAddress,
  SchedulingClassValuePair: v2_0440_SchedulingClassValuePair,
  SequenceID: v2_0440_SequenceID,
  StructuredNumeric: v2_0440_StructuredNumeric,
  StringOfTelephoneNumberDigits: v2_0440_StringOfTelephoneNumberDigits,
  SpecialtyDescription: v2_0440_SpecialtyDescription,
  SpecimenSource: v2_0440_SpecimenSource,
  SortOrder: v2_0440_SortOrder,
  StringData: v2_0440_StringData,
  Time: v2_0440_Time,
  TelephoneNumber: v2_0440_TelephoneNumber,
  TimingQuantity: v2_0440_TimingQuantity,
  TimeStamp: v2_0440_TimeStamp,
  TextData: v2_0440_TextData,
  UBValueCodeAndAmount: v2_0440_UBValueCodeAndAmount,
  VisitingHours: v2_0440_VisitingHours,
  VersionIdentifier: v2_0440_VersionIdentifier,
  ValueRange: v2_0440_ValueRange,
  ChannelIdentifier: v2_0440_ChannelIdentifier,
  WaveformSource: v2_0440_WaveformSource,
  ExtendedAddress: v2_0440_ExtendedAddress,
  ExtendedCompositeIDNumberAndNameForPersons: v2_0440_ExtendedCompositeIDNumberAndNameForPersons,
  ExtendedCompositeNameAndIDNumberForOrganizations: v2_0440_ExtendedCompositeNameAndIDNumberForOrganizations,
  ExtendedPersonName: v2_0440_ExtendedPersonName,
  ExtendedTelecommunicationsNumber: v2_0440_ExtendedTelecommunicationsNumber,
};
const v2_0441_Active: Coding = {
  code: 'A',
  display: 'Active',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0441',
};
const v2_0441_Inactive: Coding = {
  code: 'I',
  display: 'Inactive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0441',
};
const v2_0441_InactiveLostToFollowUpCancelContract: Coding = {
  code: 'L',
  display: 'Inactive - Lost to follow-up (cancel contract)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0441',
};
const v2_0441_InactiveMovedOrGoneElsewhereCancelContract: Coding = {
  code: 'M',
  display: 'Inactive - Moved or gone elsewhere (cancel contract)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0441',
};
const v2_0441_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0441',
};
const v2_0441_InactivePermanentlyInactiveDoNotReactivateOrAddNewEntriesToTheRecord: Coding = {
  code: 'P',
  display: 'Inactive - Permanently inactive (Do not reactivate or add new entries to the record)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0441',
};
const v2_0441_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0441',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0441 ( Immunization registry status)
 */
export const V20441 = {
  Active: v2_0441_Active,
  Inactive: v2_0441_Inactive,
  InactiveLostToFollowUpCancelContract: v2_0441_InactiveLostToFollowUpCancelContract,
  InactiveMovedOrGoneElsewhereCancelContract: v2_0441_InactiveMovedOrGoneElsewhereCancelContract,
  Other: v2_0441_Other,
  InactivePermanentlyInactiveDoNotReactivateOrAddNewEntriesToTheRecord: v2_0441_InactivePermanentlyInactiveDoNotReactivateOrAddNewEntriesToTheRecord,
  Unknown: v2_0441_Unknown,
};
const v2_0442_Diagnostic: Coding = {
  code: 'D',
  display: 'Diagnostic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0442',
};
const v2_0442_EmergencyRoomCasualty: Coding = {
  code: 'E',
  display: 'Emergency Room Casualty',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0442',
};
const v2_0442_PrimaryCare: Coding = {
  code: 'P',
  display: 'Primary Care',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0442',
};
const v2_0442_Therapeutic: Coding = {
  code: 'T',
  display: 'Therapeutic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0442',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0442 ( Location service code)
 */
export const V20442 = {
  Diagnostic: v2_0442_Diagnostic,
  EmergencyRoomCasualty: v2_0442_EmergencyRoomCasualty,
  PrimaryCare: v2_0442_PrimaryCare,
  Therapeutic: v2_0442_Therapeutic,
};
const v2_0443_Admitting: Coding = {
  code: 'AD',
  display: 'Admitting',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_AssistantAlternateInterpreter: Coding = {
  code: 'AI',
  display: 'Assistant/Alternate Interpreter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_Attending: Coding = {
  code: 'AT',
  display: 'Attending',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_CollectingProvider: Coding = {
  code: 'CLP',
  display: 'Collecting Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_ConsultingProvider: Coding = {
  code: 'CP',
  display: 'Consulting Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_DispensingProvider: Coding = {
  code: 'DP',
  display: 'Dispensing Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_EnteringProviderProbablyNotTheSameAsTranscriptionistQuestion: Coding = {
  code: 'EP',
  display: 'Entering Provider (probably not the same as transcriptionist?)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_FamilyHealthCareProfessional: Coding = {
  code: 'FHCP',
  display: 'Family Health Care Professional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_InitiatingProviderAsInActionBy: Coding = {
  code: 'IP',
  display: 'Initiating Provider (as in action by)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_MedicalDirector: Coding = {
  code: 'MDIR',
  display: 'Medical Director',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_PharmacistNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID: Coding = {
  code: 'PH',
  display: "Pharmacist   (not sure how to dissect Pharmacist/Treatment Supplier's Verifier ID)",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_PrimaryInterpreter: Coding = {
  code: 'PI',
  display: 'Primary Interpreter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_PrimaryCareProvider: Coding = {
  code: 'PP',
  display: 'Primary Care Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_ResponsibleObserver: Coding = {
  code: 'RO',
  display: 'Responsible Observer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_ReferringProvider: Coding = {
  code: 'RP',
  display: 'Referring Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_ReferredToProvider: Coding = {
  code: 'RT',
  display: 'Referred to Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_Technician: Coding = {
  code: 'TN',
  display: 'Technician',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_Transcriptionist: Coding = {
  code: 'TR',
  display: 'Transcriptionist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_VerifyingProvider: Coding = {
  code: 'VP',
  display: 'Verifying Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_VerifyingPharmaceuticalSupplierNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID: Coding = {
  code: 'VPS',
  display: "Verifying Pharmaceutical Supplier   (not sure how to dissect Pharmacist/Treatment Supplier's Verifier ID)",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
const v2_0443_VerifyingTreatmentSupplierNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID: Coding = {
  code: 'VTS',
  display: "Verifying Treatment Supplier   (not sure how to dissect Pharmacist/Treatment Supplier's Verifier ID)",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0443',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0443 ( Provider role)
 */
export const V20443 = {
  Admitting: v2_0443_Admitting,
  AssistantAlternateInterpreter: v2_0443_AssistantAlternateInterpreter,
  AdministeringProvider: v2_0443_AdministeringProvider,
  Attending: v2_0443_Attending,
  CollectingProvider: v2_0443_CollectingProvider,
  ConsultingProvider: v2_0443_ConsultingProvider,
  DispensingProvider: v2_0443_DispensingProvider,
  EnteringProviderProbablyNotTheSameAsTranscriptionistQuestion: v2_0443_EnteringProviderProbablyNotTheSameAsTranscriptionistQuestion,
  FamilyHealthCareProfessional: v2_0443_FamilyHealthCareProfessional,
  InitiatingProviderAsInActionBy: v2_0443_InitiatingProviderAsInActionBy,
  MedicalDirector: v2_0443_MedicalDirector,
  OrderingProvider: v2_0443_OrderingProvider,
  PharmacistNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID: v2_0443_PharmacistNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID,
  PrimaryInterpreter: v2_0443_PrimaryInterpreter,
  PrimaryCareProvider: v2_0443_PrimaryCareProvider,
  ResponsibleObserver: v2_0443_ResponsibleObserver,
  ReferringProvider: v2_0443_ReferringProvider,
  ReferredToProvider: v2_0443_ReferredToProvider,
  Technician: v2_0443_Technician,
  Transcriptionist: v2_0443_Transcriptionist,
  VerifyingProvider: v2_0443_VerifyingProvider,
  VerifyingPharmaceuticalSupplierNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID: v2_0443_VerifyingPharmaceuticalSupplierNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID,
  VerifyingTreatmentSupplierNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID: v2_0443_VerifyingTreatmentSupplierNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID,
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0444 ( Name assembly order)
 */
export const V20444 = {
  PrefixFamilyMiddleGivenSuffix: v2_0444_PrefixFamilyMiddleGivenSuffix,
  PrefixGivenMiddleFamilySuffix: v2_0444_PrefixGivenMiddleFamilySuffix,
};
const v2_0445_PatientPersonNameIsAnAlias: Coding = {
  code: 'AL',
  display: 'Patient/Person Name is an Alias',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0445',
};
const v2_0445_UnknownDefaultAddress: Coding = {
  code: 'UA',
  display: 'Unknown/Default Address',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0445',
};
const v2_0445_UnknownDefaultDateOfBirth: Coding = {
  code: 'UD',
  display: 'Unknown/Default Date of Birth',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0445',
};
const v2_0445_UnknownDefaultSocialSecurityNumber: Coding = {
  code: 'US',
  display: 'Unknown/Default Social Security Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0445',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0445 ( Identity Reliability Code)
 */
export const V20445 = {
  PatientPersonNameIsAnAlias: v2_0445_PatientPersonNameIsAnAlias,
  UnknownDefaultAddress: v2_0445_UnknownDefaultAddress,
  UnknownDefaultDateOfBirth: v2_0445_UnknownDefaultDateOfBirth,
  UnknownDefaultSocialSecurityNumber: v2_0445_UnknownDefaultSocialSecurityNumber,
};
const v2_0450_LogEvent: Coding = {
  code: 'LOG',
  display: 'Log Event',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0450',
};
const v2_0450_ServiceEvent: Coding = {
  code: 'SER',
  display: 'Service Event',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0450',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0450 ( Event type)
 */
export const V20450 = {
  LogEvent: v2_0450_LogEvent,
  ServiceEvent: v2_0450_ServiceEvent,
};
const v2_0455_NoSuggestedValuesDefined: Coding = {
  code: '...',
  display: 'No suggested values defined',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0455',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0455 ( Type of bill code)
 */
export const V20455 = {
  NoSuggestedValuesDefined: v2_0455_NoSuggestedValuesDefined,
};
const v2_0456_NoSuggestedValuesDefined: Coding = {
  code: '...',
  display: 'No suggested values defined',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0456',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0456 ( Revenue code)
 */
export const V20456 = {
  NoSuggestedValuesDefined: v2_0456_NoSuggestedValuesDefined,
};
const v2_0457_NoEditsPresentOnClaim: Coding = {
  code: '0',
  display: 'No edits present on claim',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0457',
};
const v2_0457_OnlyEditsPresentAreForLineItemDenialOrRejection: Coding = {
  code: '1',
  display: 'Only edits present are for line item denial or rejection',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0457',
};
const v2_0457_MultipleDayClaimWithOneOrMoreDaysDeniedOrRejected: Coding = {
  code: '2',
  display: 'Multiple-day claim with one or more days denied or rejected',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0457',
};
const v2_0457_ClaimDeniedRejectedSuspendedOrReturnedToProviderWithOnlyPostPaymentEdits: Coding = {
  code: '3',
  display: 'Claim denied, rejected, suspended or returned to provider with only post payment edits',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0457',
};
const v2_0457_ClaimDeniedRejectedSuspendedOrReturnedToProviderWithOnlyPrePaymentEdits: Coding = {
  code: '4',
  display: 'Claim denied, rejected, suspended or returned to provider with only pre payment edits',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0457',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0457 ( Overall claim disposition code)
 */
export const V20457 = {
  NoEditsPresentOnClaim: v2_0457_NoEditsPresentOnClaim,
  OnlyEditsPresentAreForLineItemDenialOrRejection: v2_0457_OnlyEditsPresentAreForLineItemDenialOrRejection,
  MultipleDayClaimWithOneOrMoreDaysDeniedOrRejected: v2_0457_MultipleDayClaimWithOneOrMoreDaysDeniedOrRejected,
  ClaimDeniedRejectedSuspendedOrReturnedToProviderWithOnlyPostPaymentEdits: v2_0457_ClaimDeniedRejectedSuspendedOrReturnedToProviderWithOnlyPostPaymentEdits,
  ClaimDeniedRejectedSuspendedOrReturnedToProviderWithOnlyPrePaymentEdits: v2_0457_ClaimDeniedRejectedSuspendedOrReturnedToProviderWithOnlyPrePaymentEdits,
};
const v2_0459_OCELineItemDenialOrRejectionIsNotIgnored: Coding = {
  code: '0',
  display: 'OCE line item denial or rejection is not ignored',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0459',
};
const v2_0459_OCELineItemDenialOrRejectionIsIgnored: Coding = {
  code: '1',
  display: 'OCE line item denial or rejection is ignored',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0459',
};
const v2_0459_ExternalLineItemDenialLineItemIsDeniedEvenIfNoOCEEdits: Coding = {
  code: '2',
  display: 'External line item denial. Line item is denied even if no OCE edits',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0459',
};
const v2_0459_ExternalLineItemRejectionLineItemIsRejectedEvenIfNoOCEEdits: Coding = {
  code: '3',
  display: 'External line item rejection. Line item is rejected even if no OCE edits',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0459',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0459 ( Reimbursement Action Code)
 */
export const V20459 = {
  OCELineItemDenialOrRejectionIsNotIgnored: v2_0459_OCELineItemDenialOrRejectionIsNotIgnored,
  OCELineItemDenialOrRejectionIsIgnored: v2_0459_OCELineItemDenialOrRejectionIsIgnored,
  ExternalLineItemDenialLineItemIsDeniedEvenIfNoOCEEdits: v2_0459_ExternalLineItemDenialLineItemIsDeniedEvenIfNoOCEEdits,
  ExternalLineItemRejectionLineItemIsRejectedEvenIfNoOCEEdits: v2_0459_ExternalLineItemRejectionLineItemIsRejectedEvenIfNoOCEEdits,
};
const v2_0460_LineItemNotDeniedOrRejected: Coding = {
  code: '0',
  display: 'Line item not denied or rejected',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0460',
};
const v2_0460_LineItemDeniedOrRejected: Coding = {
  code: '1',
  display: 'Line item denied or rejected',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0460',
};
const v2_0460_LineItemIsOnAMultipleDayClaimTheLineItemIsNotDeniedOrRejectedButOccursOnADayThatHasBeenDeniedOrRejected: Coding = {
  code: '2',
  display:
    'Line item is on a multiple-day claim. The line item is not denied or rejected, but occurs on a day that has been denied or rejected.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0460',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0460 ( Denial or rejection code)
 */
export const V20460 = {
  LineItemNotDeniedOrRejected: v2_0460_LineItemNotDeniedOrRejected,
  LineItemDeniedOrRejected: v2_0460_LineItemDeniedOrRejected,
  LineItemIsOnAMultipleDayClaimTheLineItemIsNotDeniedOrRejectedButOccursOnADayThatHasBeenDeniedOrRejected: v2_0460_LineItemIsOnAMultipleDayClaimTheLineItemIsNotDeniedOrRejectedButOccursOnADayThatHasBeenDeniedOrRejected,
};
const v2_0465_AlphabeticIEDefaultOrSomeSingleByte: Coding = {
  code: 'A',
  display: 'Alphabetic (i.e., Default or some single-byte)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0465',
};
const v2_0465_IdeographicIEKanji: Coding = {
  code: 'I',
  display: 'Ideographic (i.e., Kanji)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0465',
};
const v2_0465_PhoneticIEASCIIKatakanaHiraganaEtc: Coding = {
  code: 'P',
  display: 'Phonetic (i.e., ASCII, Katakana, Hiragana, etc.)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0465',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0465 ( Name/address representation)
 */
export const V20465 = {
  AlphabeticIEDefaultOrSomeSingleByte: v2_0465_AlphabeticIEDefaultOrSomeSingleByte,
  IdeographicIEKanji: v2_0465_IdeographicIEKanji,
  PhoneticIEASCIIKatakanaHiraganaEtc: v2_0465_PhoneticIEASCIIKatakanaHiraganaEtc,
};
const v2_0466_NoSuggestedValuesDefined: Coding = {
  code: '...',
  display: 'No suggested values defined',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0466',
};
const v2_0466_DentalProcedures: Coding = {
  code: '031',
  display: 'Dental procedures',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0466',
};
const v2_0466_ExcisionBiopsy: Coding = {
  code: '163',
  display: 'Excision/biopsy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0466',
};
const v2_0466_Level1SkinRepair: Coding = {
  code: '181',
  display: 'Level 1 skin repair.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0466',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0466 ( Ambulatory payment classification code)
 */
export const V20466 = {
  NoSuggestedValuesDefined: v2_0466_NoSuggestedValuesDefined,
  DentalProcedures: v2_0466_DentalProcedures,
  ExcisionBiopsy: v2_0466_ExcisionBiopsy,
  Level1SkinRepair: v2_0466_Level1SkinRepair,
};
const v2_0468_NoPaymentAdjustment: Coding = {
  code: '1',
  display: 'No payment adjustment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0468',
};
const v2_0468_DesignatedCurrentDrugOrBiologicalPaymentAdjustmentAppliesToAPCStatusIndicatorG: Coding = {
  code: '2',
  display: 'Designated current drug or biological payment adjustment applies to APC (status indicator G)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0468',
};
const v2_0468_DesignatedNewDevicePaymentAdjustmentAppliesToAPCStatusIndicatorH: Coding = {
  code: '3',
  display: 'Designated new device payment adjustment applies to APC (status indicator H)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0468',
};
const v2_0468_DesignatedNewDrugOrNewBiologicalPaymentAdjustmentAppliesToAPCStatusIndicatorJ: Coding = {
  code: '4',
  display: 'Designated new drug or new biological payment adjustment applies to APC (status indicator J)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0468',
};
const v2_0468_DeductibleNotApplicableSpecificListOfHCPCSCodes: Coding = {
  code: '5',
  display: 'Deductible not applicable (specific list of HCPCS codes)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0468',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0468 ( Payment adjustment code)
 */
export const V20468 = {
  NoPaymentAdjustment: v2_0468_NoPaymentAdjustment,
  DesignatedCurrentDrugOrBiologicalPaymentAdjustmentAppliesToAPCStatusIndicatorG: v2_0468_DesignatedCurrentDrugOrBiologicalPaymentAdjustmentAppliesToAPCStatusIndicatorG,
  DesignatedNewDevicePaymentAdjustmentAppliesToAPCStatusIndicatorH: v2_0468_DesignatedNewDevicePaymentAdjustmentAppliesToAPCStatusIndicatorH,
  DesignatedNewDrugOrNewBiologicalPaymentAdjustmentAppliesToAPCStatusIndicatorJ: v2_0468_DesignatedNewDrugOrNewBiologicalPaymentAdjustmentAppliesToAPCStatusIndicatorJ,
  DeductibleNotApplicableSpecificListOfHCPCSCodes: v2_0468_DeductibleNotApplicableSpecificListOfHCPCSCodes,
};
const v2_0469_NotPackaged: Coding = {
  code: '0',
  display: 'Not packaged',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0469',
};
const v2_0469_PackagedServiceStatusIndicatorNOrNoHCPCSCodeAndCertainRevenueCodes: Coding = {
  code: '1',
  display: 'Packaged service (status indicator N, or no HCPCS code and certain revenue codes)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0469',
};
const v2_0469_PackagedAsPartOfPartialHospitalizationPerDiemOrDailyMentalHealthServicePerDiem: Coding = {
  code: '2',
  display: 'Packaged as part of partial hospitalization per diem or daily mental health service per diem',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0469',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0469 ( Packaging status code)
 */
export const V20469 = {
  NotPackaged: v2_0469_NotPackaged,
  PackagedServiceStatusIndicatorNOrNoHCPCSCodeAndCertainRevenueCodes: v2_0469_PackagedServiceStatusIndicatorNOrNoHCPCSCodeAndCertainRevenueCodes,
  PackagedAsPartOfPartialHospitalizationPerDiemOrDailyMentalHealthServicePerDiem: v2_0469_PackagedAsPartOfPartialHospitalizationPerDiemOrDailyMentalHealthServicePerDiem,
};
const v2_0470_CornealTissueAPC: Coding = {
  code: 'Crnl',
  display: 'Corneal Tissue APC',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0470',
};
const v2_0470_DurableMedicalEquipment: Coding = {
  code: 'DME',
  display: 'Durable Medical Equipment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0470',
};
const v2_0470_Epotein: Coding = {
  code: 'EPO',
  display: 'Epotein',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0470',
};
const v2_0470_ClinicalLaboratoryAPC: Coding = {
  code: 'Lab',
  display: 'Clinical Laboratory APC',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0470',
};
const v2_0470_ScreeningMammographyAPC: Coding = {
  code: 'Mamm',
  display: 'Screening Mammography APC',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0470',
};
const v2_0470_ThisAPCIsNotPaid: Coding = {
  code: 'NoPay',
  display: 'This APC is not paid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0470',
};
const v2_0470_OutpatientProspectivePaymentSystem: Coding = {
  code: 'OPPS',
  display: 'Outpatient Prospective Payment System',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0470',
};
const v2_0470_PartialHospitalizationAPC: Coding = {
  code: 'PartH',
  display: 'Partial Hospitalization APC',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0470',
};
const v2_0470_PackagedAPC: Coding = {
  code: 'Pckg',
  display: 'Packaged APC',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0470',
};
const v2_0470_TherapyAPC: Coding = {
  code: 'Thrpy',
  display: 'Therapy APC',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0470',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0470 ( Reimbursement type code)
 */
export const V20470 = {
  CornealTissueAPC: v2_0470_CornealTissueAPC,
  DurableMedicalEquipment: v2_0470_DurableMedicalEquipment,
  Epotein: v2_0470_Epotein,
  ClinicalLaboratoryAPC: v2_0470_ClinicalLaboratoryAPC,
  ScreeningMammographyAPC: v2_0470_ScreeningMammographyAPC,
  ThisAPCIsNotPaid: v2_0470_ThisAPCIsNotPaid,
  OutpatientProspectivePaymentSystem: v2_0470_OutpatientProspectivePaymentSystem,
  PartialHospitalizationAPC: v2_0470_PartialHospitalizationAPC,
  PackagedAPC: v2_0470_PackagedAPC,
  TherapyAPC: v2_0470_TherapyAPC,
};
const v2_0472_Asynchronous: Coding = {
  code: 'A',
  display: 'Asynchronous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0472',
};
const v2_0472_ActuationTime: Coding = {
  code: 'C',
  display: 'Actuation Time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0472',
};
const v2_0472_Synchronous: Coding = {
  code: 'S',
  display: 'Synchronous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0472',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0472 ( TQ Conjunction ID)
 */
export const V20472 = {
  Asynchronous: v2_0472_Asynchronous,
  ActuationTime: v2_0472_ActuationTime,
  Synchronous: v2_0472_Synchronous,
};
const v2_0473_ThisObservationServiceIsOnTheFormularyAndHasGuidelines: Coding = {
  code: 'G',
  display: 'This observation/service is on the formulary, and has guidelines',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0473',
};
const v2_0473_ThisObservationServiceIsNotOnTheFormulary: Coding = {
  code: 'N',
  display: 'This observation/service is not on the formulary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0473',
};
const v2_0473_ThisObservationServiceIsOnTheFormularyButIsRestricted: Coding = {
  code: 'R',
  display: 'This observation/service is on the formulary, but is restricted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0473',
};
const v2_0473_ThisObservationServiceIsOnTheFormulary: Coding = {
  code: 'Y',
  display: 'This observation/service is on the formulary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0473',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0473 ( Formulary status)
 */
export const V20473 = {
  ThisObservationServiceIsOnTheFormularyAndHasGuidelines: v2_0473_ThisObservationServiceIsOnTheFormularyAndHasGuidelines,
  ThisObservationServiceIsNotOnTheFormulary: v2_0473_ThisObservationServiceIsNotOnTheFormulary,
  ThisObservationServiceIsOnTheFormularyButIsRestricted: v2_0473_ThisObservationServiceIsOnTheFormularyButIsRestricted,
  ThisObservationServiceIsOnTheFormulary: v2_0473_ThisObservationServiceIsOnTheFormulary,
};
const v2_0474_Department: Coding = {
  code: 'D',
  display: 'Department',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0474',
};
const v2_0474_Facility: Coding = {
  code: 'F',
  display: 'Facility',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0474',
};
const v2_0474_Subdivision: Coding = {
  code: 'S',
  display: 'Subdivision',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0474',
};
const v2_0474_Subdepartment: Coding = {
  code: 'U',
  display: 'Subdepartment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0474',
};
const v2_0474_Division: Coding = {
  code: 'V',
  display: 'Division',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0474',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0474 ( Practitioner Organization Unit Type)
 */
export const V20474 = {
  Department: v2_0474_Department,
  Facility: v2_0474_Facility,
  Subdivision: v2_0474_Subdivision,
  Subdepartment: v2_0474_Subdepartment,
  Division: v2_0474_Division,
};
const v2_0475_Allergy: Coding = {
  code: '01',
  display: 'Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0475',
};
const v2_0475_Intolerance: Coding = {
  code: '02',
  display: 'Intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0475',
};
const v2_0475_TreatmentFailure: Coding = {
  code: '03',
  display: 'Treatment Failure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0475',
};
const v2_0475_PatientRequest: Coding = {
  code: '04',
  display: 'Patient Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0475',
};
const v2_0475_NoException: Coding = {
  code: '05',
  display: 'No Exception',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0475',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0475 ( Charge Type Reason)
 */
export const V20475 = {
  Allergy: v2_0475_Allergy,
  Intolerance: v2_0475_Intolerance,
  TreatmentFailure: v2_0475_TreatmentFailure,
  PatientRequest: v2_0475_PatientRequest,
  NoException: v2_0475_NoException,
};
const v2_0477_ScheduleI: Coding = {
  code: 'I',
  display: 'Schedule I',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0477',
};
const v2_0477_ScheduleII: Coding = {
  code: 'II',
  display: 'Schedule II',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0477',
};
const v2_0477_ScheduleIII: Coding = {
  code: 'III',
  display: 'Schedule III',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0477',
};
const v2_0477_ScheduleIV: Coding = {
  code: 'IV',
  display: 'Schedule IV',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0477',
};
const v2_0477_ScheduleV: Coding = {
  code: 'V',
  display: 'Schedule V',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0477',
};
const v2_0477_ScheduleVI: Coding = {
  code: 'VI',
  display: 'Schedule VI',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0477',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0477 ( Controlled Substance Schedule)
 */
export const V20477 = {
  ScheduleI: v2_0477_ScheduleI,
  ScheduleII: v2_0477_ScheduleII,
  ScheduleIII: v2_0477_ScheduleIII,
  ScheduleIV: v2_0477_ScheduleIV,
  ScheduleV: v2_0477_ScheduleV,
  ScheduleVI: v2_0477_ScheduleVI,
};
const v2_0478_PharmaceuticalSubstanceIsInTheFormularyButGuidelinesApply: Coding = {
  code: 'G',
  display: 'Pharmaceutical substance is in the formulary, but guidelines apply',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0478',
};
const v2_0478_PharmaceuticalSubstanceIsNOTInTheFormulary: Coding = {
  code: 'N',
  display: 'Pharmaceutical substance is NOT in the formulary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0478',
};
const v2_0478_PharmaceuticalSubstanceIsInTheFormularyButRestrictionsApply: Coding = {
  code: 'R',
  display: 'Pharmaceutical substance is in the formulary, but restrictions apply',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0478',
};
const v2_0478_PharmaceuticalSubstanceIsInTheFormulary: Coding = {
  code: 'Y',
  display: 'Pharmaceutical substance is in the formulary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0478',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0478 ( Formulary Status)
 */
export const V20478 = {
  PharmaceuticalSubstanceIsInTheFormularyButGuidelinesApply: v2_0478_PharmaceuticalSubstanceIsInTheFormularyButGuidelinesApply,
  PharmaceuticalSubstanceIsNOTInTheFormulary: v2_0478_PharmaceuticalSubstanceIsNOTInTheFormulary,
  PharmaceuticalSubstanceIsInTheFormularyButRestrictionsApply: v2_0478_PharmaceuticalSubstanceIsInTheFormularyButRestrictionsApply,
  PharmaceuticalSubstanceIsInTheFormulary: v2_0478_PharmaceuticalSubstanceIsInTheFormulary,
};
const v2_0480_Medication: Coding = {
  code: 'M',
  display: 'Medication',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0480',
};
const v2_0480_OtherSolutionAsMedicationOrders: Coding = {
  code: 'O',
  display: 'Other solution as medication orders',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0480',
};
const v2_0480_IVLargeVolumeSolutions: Coding = {
  code: 'S',
  display: 'IV Large Volume Solutions',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0480',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0480 ( Pharmacy Order Types)
 */
export const V20480 = {
  Medication: v2_0480_Medication,
  OtherSolutionAsMedicationOrders: v2_0480_OtherSolutionAsMedicationOrders,
  IVLargeVolumeSolutions: v2_0480_IVLargeVolumeSolutions,
};
const v2_0482_InpatientOrder: Coding = {
  code: 'I',
  display: 'Inpatient Order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0482',
};
const v2_0482_OutpatientOrder: Coding = {
  code: 'O',
  display: 'Outpatient Order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0482',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0482 ( Order Type)
 */
export const V20482 = {
  InpatientOrder: v2_0482_InpatientOrder,
  OutpatientOrder: v2_0482_OutpatientOrder,
};
const v2_0483_Electronic: Coding = {
  code: 'EL',
  display: 'Electronic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0483',
};
const v2_0483_EMail: Coding = {
  code: 'EM',
  display: 'E-mail',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0483',
};
const v2_0483_Fax: Coding = {
  code: 'FX',
  display: 'Fax',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0483',
};
const v2_0483_InPerson: Coding = {
  code: 'IP',
  display: 'In Person',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0483',
};
const v2_0483_Mail: Coding = {
  code: 'MA',
  display: 'Mail',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0483',
};
const v2_0483_Paper: Coding = {
  code: 'PA',
  display: 'Paper',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0483',
};
const v2_0483_Phone: Coding = {
  code: 'PH',
  display: 'Phone',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0483',
};
const v2_0483_ReflexiveAutomatedSystem: Coding = {
  code: 'RE',
  display: 'Reflexive (Automated system)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0483',
};
const v2_0483_VideoConference: Coding = {
  code: 'VC',
  display: 'Video-conference',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0483',
};
const v2_0483_Voice: Coding = {
  code: 'VO',
  display: 'Voice',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0483',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0483 ( Authorization Mode)
 */
export const V20483 = {
  Electronic: v2_0483_Electronic,
  EMail: v2_0483_EMail,
  Fax: v2_0483_Fax,
  InPerson: v2_0483_InPerson,
  Mail: v2_0483_Mail,
  Paper: v2_0483_Paper,
  Phone: v2_0483_Phone,
  ReflexiveAutomatedSystem: v2_0483_ReflexiveAutomatedSystem,
  VideoConference: v2_0483_VideoConference,
  Voice: v2_0483_Voice,
};
const v2_0484_TrialQuantityBalance: Coding = {
  code: 'B',
  display: 'Trial Quantity Balance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0484',
};
const v2_0484_CompassionateFill: Coding = {
  code: 'C',
  display: 'Compassionate Fill',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0484',
};
const v2_0484_NewRenewFullFill: Coding = {
  code: 'N',
  display: 'New/Renew - Full Fill',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0484',
};
const v2_0484_NewRenewPartFill: Coding = {
  code: 'P',
  display: 'New/Renew - Part Fill',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0484',
};
const v2_0484_RefillPartFill: Coding = {
  code: 'Q',
  display: 'Refill - Part Fill',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0484',
};
const v2_0484_RefillFullFill: Coding = {
  code: 'R',
  display: 'Refill - Full Fill',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0484',
};
const v2_0484_ManufacturerSample: Coding = {
  code: 'S',
  display: 'Manufacturer Sample',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0484',
};
const v2_0484_TrialQuantity: Coding = {
  code: 'T',
  display: 'Trial Quantity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0484',
};
const v2_0484_NonPrescriptionFill: Coding = {
  code: 'Z',
  display: 'Non-Prescription Fill',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0484',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0484 ( Dispense Type)
 */
export const V20484 = {
  TrialQuantityBalance: v2_0484_TrialQuantityBalance,
  CompassionateFill: v2_0484_CompassionateFill,
  NewRenewFullFill: v2_0484_NewRenewFullFill,
  NewRenewPartFill: v2_0484_NewRenewPartFill,
  RefillPartFill: v2_0484_RefillPartFill,
  RefillFullFill: v2_0484_RefillFullFill,
  ManufacturerSample: v2_0484_ManufacturerSample,
  TrialQuantity: v2_0484_TrialQuantity,
  NonPrescriptionFill: v2_0484_NonPrescriptionFill,
};
const v2_0485_ASAP: Coding = {
  code: 'A',
  display: 'ASAP',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0485',
};
const v2_0485_Callback: Coding = {
  code: 'C',
  display: 'Callback',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0485',
};
const v2_0485_Preop: Coding = {
  code: 'P',
  display: 'Preop',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0485',
};
const v2_0485_AsNeeded: Coding = {
  code: 'PRN',
  display: 'As needed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0485',
};
const v2_0485_Routine: Coding = {
  code: 'R',
  display: 'Routine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0485',
};
const v2_0485_Stat: Coding = {
  code: 'S',
  display: 'Stat',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0485',
};
const v2_0485_TimingCritical: Coding = {
  code: 'T',
  display: 'Timing critical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0485',
};
const v2_0485_TimingCriticalWithinLessThanIntegerGreaterThanDays: Coding = {
  code: 'TD<integer>',
  display: 'Timing critical within <integer> days.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0485',
};
const v2_0485_TimingCriticalWithinLessThanIntegerGreaterThanHours: Coding = {
  code: 'TH<integer>',
  display: 'Timing critical within <integer> hours.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0485',
};
const v2_0485_TimingCriticalWithinLessThanIntegerGreaterThanMonths: Coding = {
  code: 'TL<integer>',
  display: 'Timing critical within <integer> months.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0485',
};
const v2_0485_TimingCriticalWithinLessThanIntegerGreaterThanMinutes: Coding = {
  code: 'TM<integer>',
  display: 'Timing critical within <integer> minutes.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0485',
};
const v2_0485_TimingCriticalWithinLessThanIntegerGreaterThanSeconds: Coding = {
  code: 'TS<integer>',
  display: 'Timing critical within <integer> seconds.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0485',
};
const v2_0485_TimingCriticalWithinLessThanIntegerGreaterThanWeeks: Coding = {
  code: 'TW<integer>',
  display: 'Timing critical within <integer> weeks.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0485',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0485 ( Extended Priority Codes)
 */
export const V20485 = {
  ASAP: v2_0485_ASAP,
  Callback: v2_0485_Callback,
  Preop: v2_0485_Preop,
  AsNeeded: v2_0485_AsNeeded,
  Routine: v2_0485_Routine,
  Stat: v2_0485_Stat,
  TimingCritical: v2_0485_TimingCritical,
  TimingCriticalWithinLessThanIntegerGreaterThanDays: v2_0485_TimingCriticalWithinLessThanIntegerGreaterThanDays,
  TimingCriticalWithinLessThanIntegerGreaterThanHours: v2_0485_TimingCriticalWithinLessThanIntegerGreaterThanHours,
  TimingCriticalWithinLessThanIntegerGreaterThanMonths: v2_0485_TimingCriticalWithinLessThanIntegerGreaterThanMonths,
  TimingCriticalWithinLessThanIntegerGreaterThanMinutes: v2_0485_TimingCriticalWithinLessThanIntegerGreaterThanMinutes,
  TimingCriticalWithinLessThanIntegerGreaterThanSeconds: v2_0485_TimingCriticalWithinLessThanIntegerGreaterThanSeconds,
  TimingCriticalWithinLessThanIntegerGreaterThanWeeks: v2_0485_TimingCriticalWithinLessThanIntegerGreaterThanWeeks,
};
const v2_0487_Abscess: Coding = {
  code: 'ABS',
  display: 'Abscess',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_TissueAcne: Coding = {
  code: 'ACNE',
  display: 'Tissue, Acne',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidAcne: Coding = {
  code: 'ACNFLD',
  display: 'Fluid, Acne',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AirSample: Coding = {
  code: 'AIRS',
  display: 'Air Sample',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Allograft: Coding = {
  code: 'ALL',
  display: 'Allograft',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AmnioticFluid: Coding = {
  code: 'AMN',
  display: 'Amniotic fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Amputation: Coding = {
  code: 'AMP',
  display: 'Amputation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipAngio: Coding = {
  code: 'ANGI',
  display: 'Catheter Tip, Angio',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipArterial: Coding = {
  code: 'ARTC',
  display: 'Catheter Tip, Arterial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SerumAcute: Coding = {
  code: 'ASERU',
  display: 'Serum, Acute',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Aspirate: Coding = {
  code: 'ASP',
  display: 'Aspirate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentAttest: Coding = {
  code: 'ATTE',
  display: 'Environment, Attest',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalAutoclaveAmpule: Coding = {
  code: 'AUTOA',
  display: 'Environmental, Autoclave Ampule',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalAutoclaveCapsule: Coding = {
  code: 'AUTOC',
  display: 'Environmental, Autoclave Capsule',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Autopsy: Coding = {
  code: 'AUTP',
  display: 'Autopsy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BloodBag: Coding = {
  code: 'BBL',
  display: 'Blood bag',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CystBakerQuoteS: Coding = {
  code: 'BCYST',
  display: "Cyst, Baker's",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_WholeBody: Coding = {
  code: 'BDY',
  display: 'Whole body',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BileFluid: Coding = {
  code: 'BIFL',
  display: 'Bile Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Bite: Coding = {
  code: 'BITE',
  display: 'Bite',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_WholeBlood: Coding = {
  code: 'BLD',
  display: 'Whole blood',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BloodArterial: Coding = {
  code: 'BLDA',
  display: 'Blood arterial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CordBlood: Coding = {
  code: 'BLDCO',
  display: 'Cord blood',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BloodVenous: Coding = {
  code: 'BLDV',
  display: 'Blood venous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Bleb: Coding = {
  code: 'BLEB',
  display: 'Bleb',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Blister: Coding = {
  code: 'BLIST',
  display: 'Blister',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Boil: Coding = {
  code: 'BOIL',
  display: 'Boil',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Bone: Coding = {
  code: 'BON',
  display: 'Bone',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BowelContents: Coding = {
  code: 'BOWL',
  display: 'Bowel contents',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Basophils: Coding = {
  code: 'BPH',
  display: 'Basophils',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BloodProductUnit: Coding = {
  code: 'BPU',
  display: 'Blood product unit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Burn: Coding = {
  code: 'BRN',
  display: 'Burn',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Brush: Coding = {
  code: 'BRSH',
  display: 'Brush',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BreathUseEXHLD: Coding = {
  code: 'BRTH',
  display: 'Breath (use EXHLD)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Brushing: Coding = {
  code: 'BRUS',
  display: 'Brushing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Bubo: Coding = {
  code: 'BUB',
  display: 'Bubo',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BullaBullae: Coding = {
  code: 'BULLA',
  display: 'Bulla/Bullae',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Biopsy: Coding = {
  code: 'BX',
  display: 'Biopsy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CalculusEqualsStone: Coding = {
  code: 'CALC',
  display: 'Calculus (=Stone)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Carbuncle: Coding = {
  code: 'CARBU',
  display: 'Carbuncle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Catheter: Coding = {
  code: 'CAT',
  display: 'Catheter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BiteCat: Coding = {
  code: 'CBITE',
  display: 'Bite, Cat',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CardiacMuscle: Coding = {
  code: 'CDM',
  display: 'Cardiac muscle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Clippings: Coding = {
  code: 'CLIPP',
  display: 'Clippings',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Conjunctiva: Coding = {
  code: 'CNJT',
  display: 'Conjunctiva',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Cannula: Coding = {
  code: 'CNL',
  display: 'Cannula',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Colostrum: Coding = {
  code: 'COL',
  display: 'Colostrum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BiospyCone: Coding = {
  code: 'CONE',
  display: 'Biospy, Cone',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_ScratchCat: Coding = {
  code: 'CSCR',
  display: 'Scratch, Cat',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SerumConvalescent: Coding = {
  code: 'CSERU',
  display: 'Serum, Convalescent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CerebralSpinalFluid: Coding = {
  code: 'CSF',
  display: 'Cerebral spinal fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterInsertionSite: Coding = {
  code: 'CSITE',
  display: 'Catheter Insertion Site',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidCystostomyTube: Coding = {
  code: 'CSMY',
  display: 'Fluid,  Cystostomy Tube',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidCyst: Coding = {
  code: 'CST',
  display: 'Fluid, Cyst',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BloodCellSaver: Coding = {
  code: 'CSVR',
  display: 'Blood, Cell Saver',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTip: Coding = {
  code: 'CTP',
  display: 'Catheter tip',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Curretage: Coding = {
  code: 'CUR',
  display: 'Curretage',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CervicalMucus: Coding = {
  code: 'CVM',
  display: 'Cervical Mucus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SiteCVP: Coding = {
  code: 'CVPS',
  display: 'Site, CVP',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipCVP: Coding = {
  code: 'CVPT',
  display: 'Catheter Tip, CVP',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_NoduleCystic: Coding = {
  code: 'CYN',
  display: 'Nodule, Cystic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Cyst: Coding = {
  code: 'CYST',
  display: 'Cyst',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BiteDog: Coding = {
  code: 'DBITE',
  display: 'Bite, Dog',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SputumDeepCough: Coding = {
  code: 'DCS',
  display: 'Sputum, Deep Cough',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_UlcerDecubitus: Coding = {
  code: 'DEC',
  display: 'Ulcer, Decubitus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalWaterDeionized: Coding = {
  code: 'DEION',
  display: 'Environmental, Water  (Deionized)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Dialysate: Coding = {
  code: 'DIA',
  display: 'Dialysate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DialysisFluid: Coding = {
  code: 'DIAF',
  display: 'Dialysis Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Discharge: Coding = {
  code: 'DISCHG',
  display: 'Discharge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Diverticulum: Coding = {
  code: 'DIV',
  display: 'Diverticulum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Drain: Coding = {
  code: 'DRN',
  display: 'Drain',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DrainageTube: Coding = {
  code: 'DRNG',
  display: 'Drainage, Tube',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DrainagePenrose: Coding = {
  code: 'DRNGP',
  display: 'Drainage, Penrose',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DuodenalFluid: Coding = {
  code: 'DUFL',
  display: 'Duodenal fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EarWaxCerumen: Coding = {
  code: 'EARW',
  display: 'Ear wax (cerumen)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BrushEsophageal: Coding = {
  code: 'EBRUSH',
  display: 'Brush, Esophageal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalEyeWash: Coding = {
  code: 'EEYE',
  display: 'Environmental, Eye Wash',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalEffluent: Coding = {
  code: 'EFF',
  display: 'Environmental, Effluent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Effusion: Coding = {
  code: 'EFFUS',
  display: 'Effusion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalFood: Coding = {
  code: 'EFOD',
  display: 'Environmental, Food',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalIsolette: Coding = {
  code: 'EISO',
  display: 'Environmental, Isolette',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Electrode: Coding = {
  code: 'ELT',
  display: 'Electrode',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalUnidentifiedSubstance: Coding = {
  code: 'ENVIR',
  display: 'Environmental, Unidentified Substance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Eosinophils: Coding = {
  code: 'EOS',
  display: 'Eosinophils',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalOtherSubstance: Coding = {
  code: 'EOTH',
  display: 'Environmental, Other Substance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalSoil: Coding = {
  code: 'ESOI',
  display: 'Environmental, Soil',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalSolutionSterile: Coding = {
  code: 'ESOS',
  display: 'Environmental, Solution (Sterile)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AspirateEndotrach: Coding = {
  code: 'ETA',
  display: 'Aspirate,  Endotrach',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipEndotracheal: Coding = {
  code: 'ETTP',
  display: 'Catheter Tip, Endotracheal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_TubeEndotracheal: Coding = {
  code: 'ETTUB',
  display: 'Tube, Endotracheal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalWhirlpool: Coding = {
  code: 'EWHI',
  display: 'Environmental, Whirlpool',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_GasExhaledEqualsBreath: Coding = {
  code: 'EXG',
  display: 'Gas, exhaled (=breath)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_ShuntExternal: Coding = {
  code: 'EXS',
  display: 'Shunt, External',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Exudate: Coding = {
  code: 'EXUDTE',
  display: 'Exudate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalWaterWell: Coding = {
  code: 'FAW',
  display: 'Environmental, Water  (Well)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BloodFetal: Coding = {
  code: 'FBLOOD',
  display: 'Blood, Fetal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidAbdomen: Coding = {
  code: 'FGA',
  display: 'Fluid,  Abdomen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Fibroblasts: Coding = {
  code: 'FIB',
  display: 'Fibroblasts',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Fistula: Coding = {
  code: 'FIST',
  display: 'Fistula',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidOther: Coding = {
  code: 'FLD',
  display: 'Fluid, Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Filter: Coding = {
  code: 'FLT',
  display: 'Filter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidBodyUnsp: Coding = {
  code: 'FLU',
  display: 'Fluid, Body unsp',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Fluid: Coding = {
  code: 'FLUID',
  display: 'Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipFoley: Coding = {
  code: 'FOLEY',
  display: 'Catheter Tip, Foley',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidRespiratory: Coding = {
  code: 'FRS',
  display: 'Fluid, Respiratory',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_ScalpFetal: Coding = {
  code: 'FSCLP',
  display: 'Scalp, Fetal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Furuncle: Coding = {
  code: 'FUR',
  display: 'Furuncle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Gas: Coding = {
  code: 'GAS',
  display: 'Gas',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AspirateGastric: Coding = {
  code: 'GASA',
  display: 'Aspirate, Gastric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AntrumGastric: Coding = {
  code: 'GASAN',
  display: 'Antrum, Gastric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BrushingGastric: Coding = {
  code: 'GASBR',
  display: 'Brushing, Gastric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DrainageGastric: Coding = {
  code: 'GASD',
  display: 'Drainage, Gastric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidContentsGastric: Coding = {
  code: 'GAST',
  display: 'Fluid/contents, Gastric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_GenitalLochia: Coding = {
  code: 'GENL',
  display: 'Genital lochia',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_GenitalVaginal: Coding = {
  code: 'GENV',
  display: 'Genital vaginal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Graft: Coding = {
  code: 'GRAFT',
  display: 'Graft',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_GraftSite: Coding = {
  code: 'GRAFTS',
  display: 'Graft Site',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Granuloma: Coding = {
  code: 'GRANU',
  display: 'Granuloma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterGroshong: Coding = {
  code: 'GROSH',
  display: 'Catheter, Groshong',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SolutionGastrostomy: Coding = {
  code: 'GSOL',
  display: 'Solution, Gastrostomy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BiopsyGastric: Coding = {
  code: 'GSPEC',
  display: 'Biopsy, Gastric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_TubeGastric: Coding = {
  code: 'GT',
  display: 'Tube, Gastric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DrainageTubeDrainageGastrostomy: Coding = {
  code: 'GTUBE',
  display: 'Drainage Tube, Drainage (Gastrostomy)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Hair: Coding = {
  code: 'HAR',
  display: 'Hair',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BiteHuman: Coding = {
  code: 'HBITE',
  display: 'Bite, Human',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BloodAutopsy: Coding = {
  code: 'HBLUD',
  display: 'Blood, Autopsy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipHemaquit: Coding = {
  code: 'HEMAQ',
  display: 'Catheter Tip, Hemaquit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipHemovac: Coding = {
  code: 'HEMO',
  display: 'Catheter Tip, Hemovac',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_TissueHerniated: Coding = {
  code: 'HERNI',
  display: 'Tissue, Herniated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DrainHemovac: Coding = {
  code: 'HEV',
  display: 'Drain, Hemovac',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterHickman: Coding = {
  code: 'HIC',
  display: 'Catheter, Hickman',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidHydrocele: Coding = {
  code: 'HYDC',
  display: 'Fluid, Hydrocele',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BiteInsect: Coding = {
  code: 'IBITE',
  display: 'Bite, Insect',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CystInclusion: Coding = {
  code: 'ICYST',
  display: 'Cyst, Inclusion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipIndwelling: Coding = {
  code: 'IDC',
  display: 'Catheter Tip, Indwelling',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_GasInhaled: Coding = {
  code: 'IHG',
  display: 'Gas, Inhaled',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DrainageIleostomy: Coding = {
  code: 'ILEO',
  display: 'Drainage, Ileostomy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SourceOfSpecimenIsIllegible: Coding = {
  code: 'ILLEG',
  display: 'Source of Specimen Is Illegible',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Implant: Coding = {
  code: 'IMP',
  display: 'Implant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SiteIncisionSurgical: Coding = {
  code: 'INCI',
  display: 'Site, Incision/Surgical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Infiltrate: Coding = {
  code: 'INFIL',
  display: 'Infiltrate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Insect: Coding = {
  code: 'INS',
  display: 'Insect',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipIntroducer: Coding = {
  code: 'INTRD',
  display: 'Catheter Tip, Introducer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Isolate: Coding = {
  code: 'ISLT',
  display: 'Isolate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_IntubationTube: Coding = {
  code: 'IT',
  display: 'Intubation tube',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_IntrauterineDevice: Coding = {
  code: 'IUD',
  display: 'Intrauterine Device',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipIV: Coding = {
  code: 'IVCAT',
  display: 'Catheter Tip, IV',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidIV: Coding = {
  code: 'IVFLD',
  display: 'Fluid, IV',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_TubingTipIV: Coding = {
  code: 'IVTIP',
  display: 'Tubing Tip, IV',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DrainageJejunal: Coding = {
  code: 'JEJU',
  display: 'Drainage, Jejunal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidJoint: Coding = {
  code: 'JNTFLD',
  display: 'Fluid, Joint',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DrainageJacksonPratt: Coding = {
  code: 'JP',
  display: 'Drainage, Jackson Pratt',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Lavage: Coding = {
  code: 'KELOI',
  display: 'Lavage',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidKidney: Coding = {
  code: 'KIDFLD',
  display: 'Fluid, Kidney',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_LavageBronhial: Coding = {
  code: 'LAVG',
  display: 'Lavage, Bronhial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_LavageGastric: Coding = {
  code: 'LAVGG',
  display: 'Lavage, Gastric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_LavagePeritoneal: Coding = {
  code: 'LAVGP',
  display: 'Lavage, Peritoneal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_LavagePreBronch: Coding = {
  code: 'LAVPG',
  display: 'Lavage, Pre-Bronch',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_ContactLens: Coding = {
  code: 'LENS1',
  display: 'Contact Lens',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_ContactLensCase: Coding = {
  code: 'LENS2',
  display: 'Contact Lens Case',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Lesion: Coding = {
  code: 'LESN',
  display: 'Lesion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_LiquidUnspecified: Coding = {
  code: 'LIQ',
  display: 'Liquid, Unspecified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_LiquidOther: Coding = {
  code: 'LIQO',
  display: 'Liquid, Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_LineArterial: Coding = {
  code: 'LNA',
  display: 'Line arterial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_LineVenous: Coding = {
  code: 'LNV',
  display: 'Line venous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidLumbarSac: Coding = {
  code: 'LSAC',
  display: 'Fluid, Lumbar Sac',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Lymphocytes: Coding = {
  code: 'LYM',
  display: 'Lymphocytes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Macrophages: Coding = {
  code: 'MAC',
  display: 'Macrophages',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipMakurkour: Coding = {
  code: 'MAHUR',
  display: 'Catheter Tip, Makurkour',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Marrow: Coding = {
  code: 'MAR',
  display: 'Marrow',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Mass: Coding = {
  code: 'MASS',
  display: 'Mass',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BloodMenstrual: Coding = {
  code: 'MBLD',
  display: 'Blood, Menstrual',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Meconium: Coding = {
  code: 'MEC',
  display: 'Meconium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BreastMilk: Coding = {
  code: 'MILK',
  display: 'Breast milk',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Milk: Coding = {
  code: 'MLK',
  display: 'Milk',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Mucosa: Coding = {
  code: 'MUCOS',
  display: 'Mucosa',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Mucus: Coding = {
  code: 'MUCUS',
  display: 'Mucus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Nail: Coding = {
  code: 'NAIL',
  display: 'Nail',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DrainageNasal: Coding = {
  code: 'NASDR',
  display: 'Drainage, Nasal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Needle: Coding = {
  code: 'NEDL',
  display: 'Needle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SiteNephrostomy: Coding = {
  code: 'NEPH',
  display: 'Site, Nephrostomy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AspirateNasogastric: Coding = {
  code: 'NGASP',
  display: 'Aspirate, Nasogastric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DrainageNasogastric: Coding = {
  code: 'NGAST',
  display: 'Drainage, Nasogastric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SiteNasoGastric: Coding = {
  code: 'NGS',
  display: 'Site, Naso/Gastric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_NoduleS: Coding = {
  code: 'NODUL',
  display: 'Nodule(s)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SecretionNasal: Coding = {
  code: 'NSECR',
  display: 'Secretion, Nasal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Other: Coding = {
  code: 'ORH',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_LesionOral: Coding = {
  code: 'ORL',
  display: 'Lesion, Oral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SourceOther: Coding = {
  code: 'OTH',
  display: 'Source, Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Pacemaker: Coding = {
  code: 'PACEM',
  display: 'Pacemaker',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_PancreaticFluid: Coding = {
  code: 'PAFL',
  display: 'Pancreatic fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidPericardial: Coding = {
  code: 'PCFL',
  display: 'Fluid, Pericardial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SitePeritonealDialysis: Coding = {
  code: 'PDSIT',
  display: 'Site, Peritoneal Dialysis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SitePeritonealDialysisTunnel: Coding = {
  code: 'PDTS',
  display: 'Site, Peritoneal Dialysis Tunnel',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AbscessPelvic: Coding = {
  code: 'PELVA',
  display: 'Abscess, Pelvic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_LesionPenile: Coding = {
  code: 'PENIL',
  display: 'Lesion, Penile',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AbscessPerianal: Coding = {
  code: 'PERIA',
  display: 'Abscess, Perianal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CystPilonidal: Coding = {
  code: 'PILOC',
  display: 'Cyst, Pilonidal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SitePin: Coding = {
  code: 'PINS',
  display: 'Site, Pin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SitePacemakerInsetion: Coding = {
  code: 'PIS',
  display: 'Site, Pacemaker Insetion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_PlantMaterial: Coding = {
  code: 'PLAN',
  display: 'Plant Material',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Plasma: Coding = {
  code: 'PLAS',
  display: 'Plasma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_PlasmaBag: Coding = {
  code: 'PLB',
  display: 'Plasma bag',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Placenta: Coding = {
  code: 'PLC',
  display: 'Placenta',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SerumPeakLevel: Coding = {
  code: 'PLEVS',
  display: 'Serum, Peak Level',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_PleuralFluidThoracentesisFluid: Coding = {
  code: 'PLR',
  display: 'Pleural fluid (thoracentesis fluid)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_PolymorphonuclearNeutrophils: Coding = {
  code: 'PMN',
  display: 'Polymorphonuclear neutrophils',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DrainagePenile: Coding = {
  code: 'PND',
  display: 'Drainage, Penile',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Polyps: Coding = {
  code: 'POL',
  display: 'Polyps',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_GraftSitePopliteal: Coding = {
  code: 'POPGS',
  display: 'Graft Site, Popliteal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_GraftPopliteal: Coding = {
  code: 'POPLG',
  display: 'Graft, Popliteal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SitePoplitealVein: Coding = {
  code: 'POPLV',
  display: 'Site, Popliteal Vein',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterPorta: Coding = {
  code: 'PORTA',
  display: 'Catheter, Porta',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_PlasmaPlateletPoor: Coding = {
  code: 'PPP',
  display: 'Plasma, Platelet poor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_ProstheticDevice: Coding = {
  code: 'PROST',
  display: 'Prosthetic Device',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_PlasmaPlateletRich: Coding = {
  code: 'PRP',
  display: 'Plasma, Platelet rich',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Pseudocyst: Coding = {
  code: 'PSC',
  display: 'Pseudocyst',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_WoundPuncture: Coding = {
  code: 'PUNCT',
  display: 'Wound, Puncture',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Pus: Coding = {
  code: 'PUS',
  display: 'Pus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Pustule: Coding = {
  code: 'PUSFR',
  display: 'Pustule',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Pus_2: Coding = {
  code: 'PUST',
  display: 'Pus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_QualityControl: Coding = {
  code: 'QC3',
  display: 'Quality Control',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_UrineRandom: Coding = {
  code: 'RANDU',
  display: 'Urine, Random',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Erythrocytes: Coding = {
  code: 'RBC',
  display: 'Erythrocytes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BiteReptile: Coding = {
  code: 'RBITE',
  display: 'Bite, Reptile',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DrainageRectal: Coding = {
  code: 'RECT',
  display: 'Drainage, Rectal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AbscessRectal: Coding = {
  code: 'RECTA',
  display: 'Abscess, Rectal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CystRenal: Coding = {
  code: 'RENALC',
  display: 'Cyst, Renal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidRenalCyst: Coding = {
  code: 'RENC',
  display: 'Fluid, Renal Cyst',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Respiratory: Coding = {
  code: 'RES',
  display: 'Respiratory',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Saliva: Coding = {
  code: 'SAL',
  display: 'Saliva',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_TissueKeloidScar: Coding = {
  code: 'SCAR',
  display: 'Tissue, Keloid (Scar)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipSubclavian: Coding = {
  code: 'SCLV',
  display: 'Catheter Tip, Subclavian',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AbscessScrotal: Coding = {
  code: 'SCROA',
  display: 'Abscess, Scrotal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SecretionS: Coding = {
  code: 'SECRE',
  display: 'Secretion(s)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Serum: Coding = {
  code: 'SER',
  display: 'Serum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SiteShunt: Coding = {
  code: 'SHU',
  display: 'Site, Shunt',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidShunt: Coding = {
  code: 'SHUNF',
  display: 'Fluid, Shunt',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Shunt: Coding = {
  code: 'SHUNT',
  display: 'Shunt',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Site: Coding = {
  code: 'SITE',
  display: 'Site',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BiopsySkin: Coding = {
  code: 'SKBP',
  display: 'Biopsy, Skin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Skin: Coding = {
  code: 'SKN',
  display: 'Skin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_MassSubMandibular: Coding = {
  code: 'SMM',
  display: 'Mass, Sub-Mandibular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SeminalFluid: Coding = {
  code: 'SMN',
  display: 'Seminal fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_FluidSynovialJointFluid: Coding = {
  code: 'SNV',
  display: 'Fluid, synovial (Joint fluid)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Spermatozoa: Coding = {
  code: 'SPRM',
  display: 'Spermatozoa',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipSuprapubic: Coding = {
  code: 'SPRP',
  display: 'Catheter Tip, Suprapubic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CathetherTipSuprapubic: Coding = {
  code: 'SPRPB',
  display: 'Cathether Tip, Suprapubic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalSporeStrip: Coding = {
  code: 'SPS',
  display: 'Environmental, Spore Strip',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Sputum: Coding = {
  code: 'SPT',
  display: 'Sputum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SputumCoughed: Coding = {
  code: 'SPTC',
  display: 'Sputum - coughed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SputumTrachealAspirate: Coding = {
  code: 'SPTT',
  display: 'Sputum - tracheal aspirate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SputumSimulated: Coding = {
  code: 'SPUT1',
  display: 'Sputum, Simulated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SputumInducted: Coding = {
  code: 'SPUTIN',
  display: 'Sputum, Inducted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SputumSpontaneous: Coding = {
  code: 'SPUTSP',
  display: 'Sputum, Spontaneous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalSterrad: Coding = {
  code: 'STER',
  display: 'Environmental, Sterrad',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_StoolEqualsFecal: Coding = {
  code: 'STL',
  display: 'Stool = Fecal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_StoneKidney: Coding = {
  code: 'STONE',
  display: 'Stone, Kidney',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AbscessSubmandibular: Coding = {
  code: 'SUBMA',
  display: 'Abscess, Submandibular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AbscessSubmaxillary: Coding = {
  code: 'SUBMX',
  display: 'Abscess, Submaxillary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_DrainageSump: Coding = {
  code: 'SUMP',
  display: 'Drainage, Sump',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SuprapubicTap: Coding = {
  code: 'SUP',
  display: 'Suprapubic Tap',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Suture: Coding = {
  code: 'SUTUR',
  display: 'Suture',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipSwanGantz: Coding = {
  code: 'SWGZ',
  display: 'Catheter Tip, Swan Gantz',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Sweat: Coding = {
  code: 'SWT',
  display: 'Sweat',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AspirateTracheal: Coding = {
  code: 'TASP',
  display: 'Aspirate, Tracheal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Tears: Coding = {
  code: 'TEAR',
  display: 'Tears',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_ThrombocytePlatelet: Coding = {
  code: 'THRB',
  display: 'Thrombocyte (platelet)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Tissue: Coding = {
  code: 'TISS',
  display: 'Tissue',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_TissueUlcer: Coding = {
  code: 'TISU',
  display: 'Tissue ulcer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CathetherTipTripleLumen: Coding = {
  code: 'TLC',
  display: 'Cathether Tip, Triple Lumen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SiteTracheostomy: Coding = {
  code: 'TRAC',
  display: 'Site, Tracheostomy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Transudate: Coding = {
  code: 'TRANS',
  display: 'Transudate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SerumTrough: Coding = {
  code: 'TSERU',
  display: 'Serum, Trough',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AbscessTesticular: Coding = {
  code: 'TSTES',
  display: 'Abscess, Testicular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_AspirateTranstracheal: Coding = {
  code: 'TTRA',
  display: 'Aspirate, Transtracheal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Tubes: Coding = {
  code: 'TUBES',
  display: 'Tubes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Tumor: Coding = {
  code: 'TUMOR',
  display: 'Tumor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SmearTzanck: Coding = {
  code: 'TZANC',
  display: 'Smear, Tzanck',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SourceUnidentified: Coding = {
  code: 'UDENT',
  display: 'Source, Unidentified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_UnknownMedicine: Coding = {
  code: 'UMED',
  display: 'Unknown Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Urine: Coding = {
  code: 'UR',
  display: 'Urine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_UrineCleanCatch: Coding = {
  code: 'URC',
  display: 'Urine clean catch',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_UrineBladderWashings: Coding = {
  code: 'URINB',
  display: 'Urine, Bladder Washings',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_UrineCatheterized: Coding = {
  code: 'URINC',
  display: 'Urine, Catheterized',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_UrineMidstream: Coding = {
  code: 'URINM',
  display: 'Urine, Midstream',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_UrineNephrostomy: Coding = {
  code: 'URINN',
  display: 'Urine, Nephrostomy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_UrinePedibag: Coding = {
  code: 'URINP',
  display: 'Urine, Pedibag',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_UrineSediment: Coding = {
  code: 'URNS',
  display: 'Urine sediment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_UrineCatheter: Coding = {
  code: 'URT',
  display: 'Urine catheter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_UrineCystoscopy: Coding = {
  code: 'USCOP',
  display: 'Urine, Cystoscopy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_SourceUnspecified: Coding = {
  code: 'USPEC',
  display: 'Source, Unspecified',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_UnkownSubstance: Coding = {
  code: 'USUB',
  display: 'Unkown substance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipVas: Coding = {
  code: 'VASTIP',
  display: 'Catheter Tip, Vas',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_CatheterTipVentricular: Coding = {
  code: 'VENT',
  display: 'Catheter Tip, Ventricular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_VitreousFluid: Coding = {
  code: 'VITF',
  display: 'Vitreous Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Vomitus: Coding = {
  code: 'VOM',
  display: 'Vomitus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Wash: Coding = {
  code: 'WASH',
  display: 'Wash',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_WashingEGBronchialWashing: Coding = {
  code: 'WASI',
  display: 'Washing, e.g. bronchial washing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Water: Coding = {
  code: 'WAT',
  display: 'Water',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_BloodWhole: Coding = {
  code: 'WB',
  display: 'Blood, Whole',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Leukocytes: Coding = {
  code: 'WBC',
  display: 'Leukocytes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Wen: Coding = {
  code: 'WEN',
  display: 'Wen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Wick: Coding = {
  code: 'WICK',
  display: 'Wick',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Wound: Coding = {
  code: 'WND',
  display: 'Wound',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_WoundAbscess: Coding = {
  code: 'WNDA',
  display: 'Wound abscess',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_WoundDrainage: Coding = {
  code: 'WNDD',
  display: 'Wound drainage',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_WoundExudate: Coding = {
  code: 'WNDE',
  display: 'Wound exudate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Worm: Coding = {
  code: 'WORM',
  display: 'Worm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_Wart: Coding = {
  code: 'WRT',
  display: 'Wart',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalWater: Coding = {
  code: 'WWA',
  display: 'Environmental, Water',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalWaterOcean: Coding = {
  code: 'WWO',
  display: 'Environmental, Water (Ocean)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
const v2_0487_EnvironmentalWaterTap: Coding = {
  code: 'WWT',
  display: 'Environmental, Water  (Tap)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0487',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0487 ( Specimen Type)
 */
export const V20487 = {
  Abscess: v2_0487_Abscess,
  TissueAcne: v2_0487_TissueAcne,
  FluidAcne: v2_0487_FluidAcne,
  AirSample: v2_0487_AirSample,
  Allograft: v2_0487_Allograft,
  AmnioticFluid: v2_0487_AmnioticFluid,
  Amputation: v2_0487_Amputation,
  CatheterTipAngio: v2_0487_CatheterTipAngio,
  CatheterTipArterial: v2_0487_CatheterTipArterial,
  SerumAcute: v2_0487_SerumAcute,
  Aspirate: v2_0487_Aspirate,
  EnvironmentAttest: v2_0487_EnvironmentAttest,
  EnvironmentalAutoclaveAmpule: v2_0487_EnvironmentalAutoclaveAmpule,
  EnvironmentalAutoclaveCapsule: v2_0487_EnvironmentalAutoclaveCapsule,
  Autopsy: v2_0487_Autopsy,
  BloodBag: v2_0487_BloodBag,
  CystBakerQuoteS: v2_0487_CystBakerQuoteS,
  WholeBody: v2_0487_WholeBody,
  BileFluid: v2_0487_BileFluid,
  Bite: v2_0487_Bite,
  WholeBlood: v2_0487_WholeBlood,
  BloodArterial: v2_0487_BloodArterial,
  CordBlood: v2_0487_CordBlood,
  BloodVenous: v2_0487_BloodVenous,
  Bleb: v2_0487_Bleb,
  Blister: v2_0487_Blister,
  Boil: v2_0487_Boil,
  Bone: v2_0487_Bone,
  BowelContents: v2_0487_BowelContents,
  Basophils: v2_0487_Basophils,
  BloodProductUnit: v2_0487_BloodProductUnit,
  Burn: v2_0487_Burn,
  Brush: v2_0487_Brush,
  BreathUseEXHLD: v2_0487_BreathUseEXHLD,
  Brushing: v2_0487_Brushing,
  Bubo: v2_0487_Bubo,
  BullaBullae: v2_0487_BullaBullae,
  Biopsy: v2_0487_Biopsy,
  CalculusEqualsStone: v2_0487_CalculusEqualsStone,
  Carbuncle: v2_0487_Carbuncle,
  Catheter: v2_0487_Catheter,
  BiteCat: v2_0487_BiteCat,
  CardiacMuscle: v2_0487_CardiacMuscle,
  Clippings: v2_0487_Clippings,
  Conjunctiva: v2_0487_Conjunctiva,
  Cannula: v2_0487_Cannula,
  Colostrum: v2_0487_Colostrum,
  BiospyCone: v2_0487_BiospyCone,
  ScratchCat: v2_0487_ScratchCat,
  SerumConvalescent: v2_0487_SerumConvalescent,
  CerebralSpinalFluid: v2_0487_CerebralSpinalFluid,
  CatheterInsertionSite: v2_0487_CatheterInsertionSite,
  FluidCystostomyTube: v2_0487_FluidCystostomyTube,
  FluidCyst: v2_0487_FluidCyst,
  BloodCellSaver: v2_0487_BloodCellSaver,
  CatheterTip: v2_0487_CatheterTip,
  Curretage: v2_0487_Curretage,
  CervicalMucus: v2_0487_CervicalMucus,
  SiteCVP: v2_0487_SiteCVP,
  CatheterTipCVP: v2_0487_CatheterTipCVP,
  NoduleCystic: v2_0487_NoduleCystic,
  Cyst: v2_0487_Cyst,
  BiteDog: v2_0487_BiteDog,
  SputumDeepCough: v2_0487_SputumDeepCough,
  UlcerDecubitus: v2_0487_UlcerDecubitus,
  EnvironmentalWaterDeionized: v2_0487_EnvironmentalWaterDeionized,
  Dialysate: v2_0487_Dialysate,
  DialysisFluid: v2_0487_DialysisFluid,
  Discharge: v2_0487_Discharge,
  Diverticulum: v2_0487_Diverticulum,
  Drain: v2_0487_Drain,
  DrainageTube: v2_0487_DrainageTube,
  DrainagePenrose: v2_0487_DrainagePenrose,
  DuodenalFluid: v2_0487_DuodenalFluid,
  EarWaxCerumen: v2_0487_EarWaxCerumen,
  BrushEsophageal: v2_0487_BrushEsophageal,
  EnvironmentalEyeWash: v2_0487_EnvironmentalEyeWash,
  EnvironmentalEffluent: v2_0487_EnvironmentalEffluent,
  Effusion: v2_0487_Effusion,
  EnvironmentalFood: v2_0487_EnvironmentalFood,
  EnvironmentalIsolette: v2_0487_EnvironmentalIsolette,
  Electrode: v2_0487_Electrode,
  EnvironmentalUnidentifiedSubstance: v2_0487_EnvironmentalUnidentifiedSubstance,
  Eosinophils: v2_0487_Eosinophils,
  EnvironmentalOtherSubstance: v2_0487_EnvironmentalOtherSubstance,
  EnvironmentalSoil: v2_0487_EnvironmentalSoil,
  EnvironmentalSolutionSterile: v2_0487_EnvironmentalSolutionSterile,
  AspirateEndotrach: v2_0487_AspirateEndotrach,
  CatheterTipEndotracheal: v2_0487_CatheterTipEndotracheal,
  TubeEndotracheal: v2_0487_TubeEndotracheal,
  EnvironmentalWhirlpool: v2_0487_EnvironmentalWhirlpool,
  GasExhaledEqualsBreath: v2_0487_GasExhaledEqualsBreath,
  ShuntExternal: v2_0487_ShuntExternal,
  Exudate: v2_0487_Exudate,
  EnvironmentalWaterWell: v2_0487_EnvironmentalWaterWell,
  BloodFetal: v2_0487_BloodFetal,
  FluidAbdomen: v2_0487_FluidAbdomen,
  Fibroblasts: v2_0487_Fibroblasts,
  Fistula: v2_0487_Fistula,
  FluidOther: v2_0487_FluidOther,
  Filter: v2_0487_Filter,
  FluidBodyUnsp: v2_0487_FluidBodyUnsp,
  Fluid: v2_0487_Fluid,
  CatheterTipFoley: v2_0487_CatheterTipFoley,
  FluidRespiratory: v2_0487_FluidRespiratory,
  ScalpFetal: v2_0487_ScalpFetal,
  Furuncle: v2_0487_Furuncle,
  Gas: v2_0487_Gas,
  AspirateGastric: v2_0487_AspirateGastric,
  AntrumGastric: v2_0487_AntrumGastric,
  BrushingGastric: v2_0487_BrushingGastric,
  DrainageGastric: v2_0487_DrainageGastric,
  FluidContentsGastric: v2_0487_FluidContentsGastric,
  GenitalLochia: v2_0487_GenitalLochia,
  GenitalVaginal: v2_0487_GenitalVaginal,
  Graft: v2_0487_Graft,
  GraftSite: v2_0487_GraftSite,
  Granuloma: v2_0487_Granuloma,
  CatheterGroshong: v2_0487_CatheterGroshong,
  SolutionGastrostomy: v2_0487_SolutionGastrostomy,
  BiopsyGastric: v2_0487_BiopsyGastric,
  TubeGastric: v2_0487_TubeGastric,
  DrainageTubeDrainageGastrostomy: v2_0487_DrainageTubeDrainageGastrostomy,
  Hair: v2_0487_Hair,
  BiteHuman: v2_0487_BiteHuman,
  BloodAutopsy: v2_0487_BloodAutopsy,
  CatheterTipHemaquit: v2_0487_CatheterTipHemaquit,
  CatheterTipHemovac: v2_0487_CatheterTipHemovac,
  TissueHerniated: v2_0487_TissueHerniated,
  DrainHemovac: v2_0487_DrainHemovac,
  CatheterHickman: v2_0487_CatheterHickman,
  FluidHydrocele: v2_0487_FluidHydrocele,
  BiteInsect: v2_0487_BiteInsect,
  CystInclusion: v2_0487_CystInclusion,
  CatheterTipIndwelling: v2_0487_CatheterTipIndwelling,
  GasInhaled: v2_0487_GasInhaled,
  DrainageIleostomy: v2_0487_DrainageIleostomy,
  SourceOfSpecimenIsIllegible: v2_0487_SourceOfSpecimenIsIllegible,
  Implant: v2_0487_Implant,
  SiteIncisionSurgical: v2_0487_SiteIncisionSurgical,
  Infiltrate: v2_0487_Infiltrate,
  Insect: v2_0487_Insect,
  CatheterTipIntroducer: v2_0487_CatheterTipIntroducer,
  Isolate: v2_0487_Isolate,
  IntubationTube: v2_0487_IntubationTube,
  IntrauterineDevice: v2_0487_IntrauterineDevice,
  CatheterTipIV: v2_0487_CatheterTipIV,
  FluidIV: v2_0487_FluidIV,
  TubingTipIV: v2_0487_TubingTipIV,
  DrainageJejunal: v2_0487_DrainageJejunal,
  FluidJoint: v2_0487_FluidJoint,
  DrainageJacksonPratt: v2_0487_DrainageJacksonPratt,
  Lavage: v2_0487_Lavage,
  FluidKidney: v2_0487_FluidKidney,
  LavageBronhial: v2_0487_LavageBronhial,
  LavageGastric: v2_0487_LavageGastric,
  LavagePeritoneal: v2_0487_LavagePeritoneal,
  LavagePreBronch: v2_0487_LavagePreBronch,
  ContactLens: v2_0487_ContactLens,
  ContactLensCase: v2_0487_ContactLensCase,
  Lesion: v2_0487_Lesion,
  LiquidUnspecified: v2_0487_LiquidUnspecified,
  LiquidOther: v2_0487_LiquidOther,
  LineArterial: v2_0487_LineArterial,
  LineVenous: v2_0487_LineVenous,
  FluidLumbarSac: v2_0487_FluidLumbarSac,
  Lymphocytes: v2_0487_Lymphocytes,
  Macrophages: v2_0487_Macrophages,
  CatheterTipMakurkour: v2_0487_CatheterTipMakurkour,
  Marrow: v2_0487_Marrow,
  Mass: v2_0487_Mass,
  BloodMenstrual: v2_0487_BloodMenstrual,
  Meconium: v2_0487_Meconium,
  BreastMilk: v2_0487_BreastMilk,
  Milk: v2_0487_Milk,
  Mucosa: v2_0487_Mucosa,
  Mucus: v2_0487_Mucus,
  Nail: v2_0487_Nail,
  DrainageNasal: v2_0487_DrainageNasal,
  Needle: v2_0487_Needle,
  SiteNephrostomy: v2_0487_SiteNephrostomy,
  AspirateNasogastric: v2_0487_AspirateNasogastric,
  DrainageNasogastric: v2_0487_DrainageNasogastric,
  SiteNasoGastric: v2_0487_SiteNasoGastric,
  NoduleS: v2_0487_NoduleS,
  SecretionNasal: v2_0487_SecretionNasal,
  Other: v2_0487_Other,
  LesionOral: v2_0487_LesionOral,
  SourceOther: v2_0487_SourceOther,
  Pacemaker: v2_0487_Pacemaker,
  PancreaticFluid: v2_0487_PancreaticFluid,
  FluidPericardial: v2_0487_FluidPericardial,
  SitePeritonealDialysis: v2_0487_SitePeritonealDialysis,
  SitePeritonealDialysisTunnel: v2_0487_SitePeritonealDialysisTunnel,
  AbscessPelvic: v2_0487_AbscessPelvic,
  LesionPenile: v2_0487_LesionPenile,
  AbscessPerianal: v2_0487_AbscessPerianal,
  CystPilonidal: v2_0487_CystPilonidal,
  SitePin: v2_0487_SitePin,
  SitePacemakerInsetion: v2_0487_SitePacemakerInsetion,
  PlantMaterial: v2_0487_PlantMaterial,
  Plasma: v2_0487_Plasma,
  PlasmaBag: v2_0487_PlasmaBag,
  Placenta: v2_0487_Placenta,
  SerumPeakLevel: v2_0487_SerumPeakLevel,
  PleuralFluidThoracentesisFluid: v2_0487_PleuralFluidThoracentesisFluid,
  PolymorphonuclearNeutrophils: v2_0487_PolymorphonuclearNeutrophils,
  DrainagePenile: v2_0487_DrainagePenile,
  Polyps: v2_0487_Polyps,
  GraftSitePopliteal: v2_0487_GraftSitePopliteal,
  GraftPopliteal: v2_0487_GraftPopliteal,
  SitePoplitealVein: v2_0487_SitePoplitealVein,
  CatheterPorta: v2_0487_CatheterPorta,
  PlasmaPlateletPoor: v2_0487_PlasmaPlateletPoor,
  ProstheticDevice: v2_0487_ProstheticDevice,
  PlasmaPlateletRich: v2_0487_PlasmaPlateletRich,
  Pseudocyst: v2_0487_Pseudocyst,
  WoundPuncture: v2_0487_WoundPuncture,
  Pus: v2_0487_Pus,
  Pustule: v2_0487_Pustule,
  Pus_2: v2_0487_Pus_2,
  QualityControl: v2_0487_QualityControl,
  UrineRandom: v2_0487_UrineRandom,
  Erythrocytes: v2_0487_Erythrocytes,
  BiteReptile: v2_0487_BiteReptile,
  DrainageRectal: v2_0487_DrainageRectal,
  AbscessRectal: v2_0487_AbscessRectal,
  CystRenal: v2_0487_CystRenal,
  FluidRenalCyst: v2_0487_FluidRenalCyst,
  Respiratory: v2_0487_Respiratory,
  Saliva: v2_0487_Saliva,
  TissueKeloidScar: v2_0487_TissueKeloidScar,
  CatheterTipSubclavian: v2_0487_CatheterTipSubclavian,
  AbscessScrotal: v2_0487_AbscessScrotal,
  SecretionS: v2_0487_SecretionS,
  Serum: v2_0487_Serum,
  SiteShunt: v2_0487_SiteShunt,
  FluidShunt: v2_0487_FluidShunt,
  Shunt: v2_0487_Shunt,
  Site: v2_0487_Site,
  BiopsySkin: v2_0487_BiopsySkin,
  Skin: v2_0487_Skin,
  MassSubMandibular: v2_0487_MassSubMandibular,
  SeminalFluid: v2_0487_SeminalFluid,
  FluidSynovialJointFluid: v2_0487_FluidSynovialJointFluid,
  Spermatozoa: v2_0487_Spermatozoa,
  CatheterTipSuprapubic: v2_0487_CatheterTipSuprapubic,
  CathetherTipSuprapubic: v2_0487_CathetherTipSuprapubic,
  EnvironmentalSporeStrip: v2_0487_EnvironmentalSporeStrip,
  Sputum: v2_0487_Sputum,
  SputumCoughed: v2_0487_SputumCoughed,
  SputumTrachealAspirate: v2_0487_SputumTrachealAspirate,
  SputumSimulated: v2_0487_SputumSimulated,
  SputumInducted: v2_0487_SputumInducted,
  SputumSpontaneous: v2_0487_SputumSpontaneous,
  EnvironmentalSterrad: v2_0487_EnvironmentalSterrad,
  StoolEqualsFecal: v2_0487_StoolEqualsFecal,
  StoneKidney: v2_0487_StoneKidney,
  AbscessSubmandibular: v2_0487_AbscessSubmandibular,
  AbscessSubmaxillary: v2_0487_AbscessSubmaxillary,
  DrainageSump: v2_0487_DrainageSump,
  SuprapubicTap: v2_0487_SuprapubicTap,
  Suture: v2_0487_Suture,
  CatheterTipSwanGantz: v2_0487_CatheterTipSwanGantz,
  Sweat: v2_0487_Sweat,
  AspirateTracheal: v2_0487_AspirateTracheal,
  Tears: v2_0487_Tears,
  ThrombocytePlatelet: v2_0487_ThrombocytePlatelet,
  Tissue: v2_0487_Tissue,
  TissueUlcer: v2_0487_TissueUlcer,
  CathetherTipTripleLumen: v2_0487_CathetherTipTripleLumen,
  SiteTracheostomy: v2_0487_SiteTracheostomy,
  Transudate: v2_0487_Transudate,
  SerumTrough: v2_0487_SerumTrough,
  AbscessTesticular: v2_0487_AbscessTesticular,
  AspirateTranstracheal: v2_0487_AspirateTranstracheal,
  Tubes: v2_0487_Tubes,
  Tumor: v2_0487_Tumor,
  SmearTzanck: v2_0487_SmearTzanck,
  SourceUnidentified: v2_0487_SourceUnidentified,
  UnknownMedicine: v2_0487_UnknownMedicine,
  Urine: v2_0487_Urine,
  UrineCleanCatch: v2_0487_UrineCleanCatch,
  UrineBladderWashings: v2_0487_UrineBladderWashings,
  UrineCatheterized: v2_0487_UrineCatheterized,
  UrineMidstream: v2_0487_UrineMidstream,
  UrineNephrostomy: v2_0487_UrineNephrostomy,
  UrinePedibag: v2_0487_UrinePedibag,
  UrineSediment: v2_0487_UrineSediment,
  UrineCatheter: v2_0487_UrineCatheter,
  UrineCystoscopy: v2_0487_UrineCystoscopy,
  SourceUnspecified: v2_0487_SourceUnspecified,
  UnkownSubstance: v2_0487_UnkownSubstance,
  CatheterTipVas: v2_0487_CatheterTipVas,
  CatheterTipVentricular: v2_0487_CatheterTipVentricular,
  VitreousFluid: v2_0487_VitreousFluid,
  Vomitus: v2_0487_Vomitus,
  Wash: v2_0487_Wash,
  WashingEGBronchialWashing: v2_0487_WashingEGBronchialWashing,
  Water: v2_0487_Water,
  BloodWhole: v2_0487_BloodWhole,
  Leukocytes: v2_0487_Leukocytes,
  Wen: v2_0487_Wen,
  Wick: v2_0487_Wick,
  Wound: v2_0487_Wound,
  WoundAbscess: v2_0487_WoundAbscess,
  WoundDrainage: v2_0487_WoundDrainage,
  WoundExudate: v2_0487_WoundExudate,
  Worm: v2_0487_Worm,
  Wart: v2_0487_Wart,
  EnvironmentalWater: v2_0487_EnvironmentalWater,
  EnvironmentalWaterOcean: v2_0487_EnvironmentalWaterOcean,
  EnvironmentalWaterTap: v2_0487_EnvironmentalWaterTap,
};
const v2_0488_PlatesAnaerobic: Coding = {
  code: 'ANP',
  display: 'Plates, Anaerobic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_PlatesBloodAgar: Coding = {
  code: 'BAP',
  display: 'Plates, Blood Agar',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_BloodCultureAerobicBottle: Coding = {
  code: 'BCAE',
  display: 'Blood Culture, Aerobic Bottle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_BloodCultureAnaerobicBottle: Coding = {
  code: 'BCAN',
  display: 'Blood Culture, Anaerobic Bottle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_BloodCulturePediatricBottle: Coding = {
  code: 'BCPD',
  display: 'Blood Culture, Pediatric Bottle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_Biopsy: Coding = {
  code: 'BIO',
  display: 'Biopsy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_CapillarySpecimen: Coding = {
  code: 'CAP',
  display: 'Capillary Specimen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_Catheterized: Coding = {
  code: 'CATH',
  display: 'Catheterized',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_LineCVP: Coding = {
  code: 'CVP',
  display: 'Line, CVP',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_EnvironmentalPlate: Coding = {
  code: 'EPLA',
  display: 'Environmental, Plate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_EnvironmentalSwab: Coding = {
  code: 'ESWA',
  display: 'Environmental, Swab',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_AspirationFineNeedle: Coding = {
  code: 'FNA',
  display: 'Aspiration, Fine Needle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_PlateCough: Coding = {
  code: 'KOFFP',
  display: 'Plate, Cough',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_LineArterial: Coding = {
  code: 'LNA',
  display: 'Line, Arterial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_LineVenous: Coding = {
  code: 'LNV',
  display: 'Line, Venous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_MartinLewisAgar: Coding = {
  code: 'MARTL',
  display: 'Martin-Lewis Agar',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_ModMartinLewisAgar: Coding = {
  code: 'ML11',
  display: 'Mod. Martin-Lewis Agar',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_PlateMartinLewis: Coding = {
  code: 'MLP',
  display: 'Plate, Martin-Lewis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_PlateNewYorkCity: Coding = {
  code: 'NYP',
  display: 'Plate, New York City',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_PaceGenProbe: Coding = {
  code: 'PACE',
  display: 'Pace, Gen-Probe',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_PinwormPrep: Coding = {
  code: 'PIN',
  display: 'Pinworm Prep',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_ArterialPuncture: Coding = {
  code: 'PNA',
  display: 'Arterial puncture',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_PumpPrime: Coding = {
  code: 'PRIME',
  display: 'Pump Prime',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_PumpSpecimen: Coding = {
  code: 'PUMP',
  display: 'Pump Specimen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_QualityControlForMicro: Coding = {
  code: 'QC5',
  display: 'Quality Control For Micro',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_ScalpFetalVein: Coding = {
  code: 'SCLP',
  display: 'Scalp, Fetal Vein',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_Scrapings: Coding = {
  code: 'SCRAPS',
  display: 'Scrapings',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_Shaving: Coding = {
  code: 'SHA',
  display: 'Shaving',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_Swab: Coding = {
  code: 'SWA',
  display: 'Swab',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_SwabDacronTipped: Coding = {
  code: 'SWD',
  display: 'Swab, Dacron tipped',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_TransportMediaAnaerobic: Coding = {
  code: 'TMAN',
  display: 'Transport Media, Anaerobic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_TransportMediaChalamydia: Coding = {
  code: 'TMCH',
  display: 'Transport Media, Chalamydia',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_TransportMediaM4: Coding = {
  code: 'TMM4',
  display: 'Transport Media, M4',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_TransportMediaMycoplasma: Coding = {
  code: 'TMMY',
  display: 'Transport Media, Mycoplasma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_TransportMedia: Coding = {
  code: 'TMOT',
  display: 'Transport Media,',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_PlateThayerMartin: Coding = {
  code: 'TMP',
  display: 'Plate, Thayer-Martin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_TransportMediaPVA: Coding = {
  code: 'TMPV',
  display: 'Transport Media, PVA',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_TransportMediaStoolCulture: Coding = {
  code: 'TMSC',
  display: 'Transport Media, Stool Culture',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_TransportMediaUreaplasma: Coding = {
  code: 'TMUP',
  display: 'Transport Media, Ureaplasma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_TransportMediaViral: Coding = {
  code: 'TMVI',
  display: 'Transport Media, Viral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_Venipuncture: Coding = {
  code: 'VENIP',
  display: 'Venipuncture',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
const v2_0488_SwabWoodenShaft: Coding = {
  code: 'WOOD',
  display: 'Swab, Wooden Shaft',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0488',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0488 ( Specimen Collection Method)
 */
export const V20488 = {
  PlatesAnaerobic: v2_0488_PlatesAnaerobic,
  PlatesBloodAgar: v2_0488_PlatesBloodAgar,
  BloodCultureAerobicBottle: v2_0488_BloodCultureAerobicBottle,
  BloodCultureAnaerobicBottle: v2_0488_BloodCultureAnaerobicBottle,
  BloodCulturePediatricBottle: v2_0488_BloodCulturePediatricBottle,
  Biopsy: v2_0488_Biopsy,
  CapillarySpecimen: v2_0488_CapillarySpecimen,
  Catheterized: v2_0488_Catheterized,
  LineCVP: v2_0488_LineCVP,
  EnvironmentalPlate: v2_0488_EnvironmentalPlate,
  EnvironmentalSwab: v2_0488_EnvironmentalSwab,
  AspirationFineNeedle: v2_0488_AspirationFineNeedle,
  PlateCough: v2_0488_PlateCough,
  LineArterial: v2_0488_LineArterial,
  LineVenous: v2_0488_LineVenous,
  MartinLewisAgar: v2_0488_MartinLewisAgar,
  ModMartinLewisAgar: v2_0488_ModMartinLewisAgar,
  PlateMartinLewis: v2_0488_PlateMartinLewis,
  PlateNewYorkCity: v2_0488_PlateNewYorkCity,
  PaceGenProbe: v2_0488_PaceGenProbe,
  PinwormPrep: v2_0488_PinwormPrep,
  ArterialPuncture: v2_0488_ArterialPuncture,
  PumpPrime: v2_0488_PumpPrime,
  PumpSpecimen: v2_0488_PumpSpecimen,
  QualityControlForMicro: v2_0488_QualityControlForMicro,
  ScalpFetalVein: v2_0488_ScalpFetalVein,
  Scrapings: v2_0488_Scrapings,
  Shaving: v2_0488_Shaving,
  Swab: v2_0488_Swab,
  SwabDacronTipped: v2_0488_SwabDacronTipped,
  TransportMediaAnaerobic: v2_0488_TransportMediaAnaerobic,
  TransportMediaChalamydia: v2_0488_TransportMediaChalamydia,
  TransportMediaM4: v2_0488_TransportMediaM4,
  TransportMediaMycoplasma: v2_0488_TransportMediaMycoplasma,
  TransportMedia: v2_0488_TransportMedia,
  PlateThayerMartin: v2_0488_PlateThayerMartin,
  TransportMediaPVA: v2_0488_TransportMediaPVA,
  TransportMediaStoolCulture: v2_0488_TransportMediaStoolCulture,
  TransportMediaUreaplasma: v2_0488_TransportMediaUreaplasma,
  TransportMediaViral: v2_0488_TransportMediaViral,
  Venipuncture: v2_0488_Venipuncture,
  SwabWoodenShaft: v2_0488_SwabWoodenShaft,
};
const v2_0489_Aggressive: Coding = {
  code: 'AGG',
  display: 'Aggressive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0489',
};
const v2_0489_Biohazard: Coding = {
  code: 'BHZ',
  display: 'Biohazard',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0489',
};
const v2_0489_Biological: Coding = {
  code: 'BIO',
  display: 'Biological',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0489',
};
const v2_0489_Corrosive: Coding = {
  code: 'COR',
  display: 'Corrosive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0489',
};
const v2_0489_EscapeRisk: Coding = {
  code: 'ESC',
  display: 'Escape Risk',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0489',
};
const v2_0489_Explosive: Coding = {
  code: 'EXP',
  display: 'Explosive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0489',
};
const v2_0489_MaterialDangerInflammable: Coding = {
  code: 'IFL',
  display: 'MaterialDangerInflammable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0489',
};
const v2_0489_MaterialDangerInfectious: Coding = {
  code: 'INF',
  display: 'MaterialDangerInfectious',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0489',
};
const v2_0489_InjuryHazard: Coding = {
  code: 'INJ',
  display: 'Injury Hazard',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0489',
};
const v2_0489_Poison: Coding = {
  code: 'POI',
  display: 'Poison',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0489',
};
const v2_0489_Radioactive: Coding = {
  code: 'RAD',
  display: 'Radioactive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0489',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0489 ( Risk Codes)
 */
export const V20489 = {
  Aggressive: v2_0489_Aggressive,
  Biohazard: v2_0489_Biohazard,
  Biological: v2_0489_Biological,
  Corrosive: v2_0489_Corrosive,
  EscapeRisk: v2_0489_EscapeRisk,
  Explosive: v2_0489_Explosive,
  MaterialDangerInflammable: v2_0489_MaterialDangerInflammable,
  MaterialDangerInfectious: v2_0489_MaterialDangerInfectious,
  InjuryHazard: v2_0489_InjuryHazard,
  Poison: v2_0489_Poison,
  Radioactive: v2_0489_Radioactive,
};
const v2_0490_Expired: Coding = {
  code: 'EX',
  display: 'Expired',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
const v2_0490_QuantityNotSufficient: Coding = {
  code: 'QS',
  display: 'Quantity not sufficient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
const v2_0490_MissingPatientIDNumber: Coding = {
  code: 'RA',
  display: 'Missing patient ID number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
const v2_0490_BrokenContainer: Coding = {
  code: 'RB',
  display: 'Broken container',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
const v2_0490_Clotting: Coding = {
  code: 'RC',
  display: 'Clotting',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
const v2_0490_MissingCollectionDate: Coding = {
  code: 'RD',
  display: 'Missing collection date',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
const v2_0490_MissingPatientName: Coding = {
  code: 'RE',
  display: 'Missing patient name',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
const v2_0490_Hemolysis: Coding = {
  code: 'RH',
  display: 'Hemolysis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
const v2_0490_IdentificationProblem: Coding = {
  code: 'RI',
  display: 'Identification problem',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
const v2_0490_Labeling: Coding = {
  code: 'RM',
  display: 'Labeling',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
const v2_0490_Contamination: Coding = {
  code: 'RN',
  display: 'Contamination',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
const v2_0490_MissingPhlebotomistID: Coding = {
  code: 'RP',
  display: 'Missing phlebotomist ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
const v2_0490_ImproperStorage: Coding = {
  code: 'RR',
  display: 'Improper storage',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
const v2_0490_NameMisspelling: Coding = {
  code: 'RS',
  display: 'Name misspelling',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0490',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0490 ( Specimen Reject Reason)
 */
export const V20490 = {
  Expired: v2_0490_Expired,
  QuantityNotSufficient: v2_0490_QuantityNotSufficient,
  MissingPatientIDNumber: v2_0490_MissingPatientIDNumber,
  BrokenContainer: v2_0490_BrokenContainer,
  Clotting: v2_0490_Clotting,
  MissingCollectionDate: v2_0490_MissingCollectionDate,
  MissingPatientName: v2_0490_MissingPatientName,
  Hemolysis: v2_0490_Hemolysis,
  IdentificationProblem: v2_0490_IdentificationProblem,
  Labeling: v2_0490_Labeling,
  Contamination: v2_0490_Contamination,
  MissingPhlebotomistID: v2_0490_MissingPhlebotomistID,
  ImproperStorage: v2_0490_ImproperStorage,
  NameMisspelling: v2_0490_NameMisspelling,
};
const v2_0491_Excellent: Coding = {
  code: 'E',
  display: 'Excellent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0491',
};
const v2_0491_Fair: Coding = {
  code: 'F',
  display: 'Fair',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0491',
};
const v2_0491_Good: Coding = {
  code: 'G',
  display: 'Good',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0491',
};
const v2_0491_Poor: Coding = {
  code: 'P',
  display: 'Poor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0491',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0491 ( Specimen Quality)
 */
export const V20491 = {
  Excellent: v2_0491_Excellent,
  Fair: v2_0491_Fair,
  Good: v2_0491_Good,
  Poor: v2_0491_Poor,
};
const v2_0492_Appropriate: Coding = {
  code: 'A',
  display: 'Appropriate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0492',
};
const v2_0492_Inappropriate: Coding = {
  code: 'I',
  display: 'Inappropriate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0492',
};
const v2_0492_Preferred: Coding = {
  code: 'P',
  display: 'Preferred',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0492',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0492 ( Specimen Appropriateness)
 */
export const V20492 = {
  Appropriate: v2_0492_Appropriate,
  Inappropriate: v2_0492_Inappropriate,
  Preferred: v2_0492_Preferred,
};
const v2_0493_Autolyzed: Coding = {
  code: 'AUT',
  display: 'Autolyzed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0493',
};
const v2_0493_Centrifuged: Coding = {
  code: 'CFU',
  display: 'Centrifuged',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0493',
};
const v2_0493_Clotted: Coding = {
  code: 'CLOT',
  display: 'Clotted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0493',
};
const v2_0493_Contaminated: Coding = {
  code: 'CON',
  display: 'Contaminated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0493',
};
const v2_0493_Cool: Coding = {
  code: 'COOL',
  display: 'Cool',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0493',
};
const v2_0493_Frozen: Coding = {
  code: 'FROZ',
  display: 'Frozen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0493',
};
const v2_0493_Hemolyzed: Coding = {
  code: 'HEM',
  display: 'Hemolyzed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0493',
};
const v2_0493_Live: Coding = {
  code: 'LIVE',
  display: 'Live',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0493',
};
const v2_0493_RoomTemperature: Coding = {
  code: 'ROOM',
  display: 'Room temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0493',
};
const v2_0493_SampleNotReceived: Coding = {
  code: 'SNR',
  display: 'Sample not received',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0493',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0493 ( Specimen Condition)
 */
export const V20493 = {
  Autolyzed: v2_0493_Autolyzed,
  Centrifuged: v2_0493_Centrifuged,
  Clotted: v2_0493_Clotted,
  Contaminated: v2_0493_Contaminated,
  Cool: v2_0493_Cool,
  Frozen: v2_0493_Frozen,
  Hemolyzed: v2_0493_Hemolyzed,
  Live: v2_0493_Live,
  RoomTemperature: v2_0493_RoomTemperature,
  SampleNotReceived: v2_0493_SampleNotReceived,
};
const v2_0494_Aliquot: Coding = {
  code: 'A',
  display: 'Aliquot',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0494',
};
const v2_0494_Component: Coding = {
  code: 'C',
  display: 'Component',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0494',
};
const v2_0494_ModifiedFromOriginalSpecimen: Coding = {
  code: 'M',
  display: 'Modified from original specimen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0494',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0494 ( Specimen Child Role)
 */
export const V20494 = {
  Aliquot: v2_0494_Aliquot,
  Component: v2_0494_Component,
  ModifiedFromOriginalSpecimen: v2_0494_ModifiedFromOriginalSpecimen,
};
const v2_0495_Anterior: Coding = {
  code: 'ANT',
  display: 'Anterior',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_Bilateral: Coding = {
  code: 'BIL',
  display: 'Bilateral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_Distal: Coding = {
  code: 'DIS',
  display: 'Distal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_External: Coding = {
  code: 'EXT',
  display: 'External',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_Left: Coding = {
  code: 'L',
  display: 'Left',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_Lateral: Coding = {
  code: 'LAT',
  display: 'Lateral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_QuadrantLeftLower: Coding = {
  code: 'LLQ',
  display: 'Quadrant, Left Lower',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_Lower: Coding = {
  code: 'LOW',
  display: 'Lower',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_QuadrantLeftUpper: Coding = {
  code: 'LUQ',
  display: 'Quadrant, Left Upper',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_Medial: Coding = {
  code: 'MED',
  display: 'Medial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_Posterior: Coding = {
  code: 'POS',
  display: 'Posterior',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_Proximal: Coding = {
  code: 'PRO',
  display: 'Proximal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_Right: Coding = {
  code: 'R',
  display: 'Right',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_QuadrantRightLower: Coding = {
  code: 'RLQ',
  display: 'Quadrant, Right Lower',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_QuadrantRightUpper: Coding = {
  code: 'RUQ',
  display: 'Quadrant, Right Upper',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
const v2_0495_Upper: Coding = {
  code: 'UPP',
  display: 'Upper',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0495',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0495 ( Body Site Modifier)
 */
export const V20495 = {
  Anterior: v2_0495_Anterior,
  Bilateral: v2_0495_Bilateral,
  Distal: v2_0495_Distal,
  External: v2_0495_External,
  Left: v2_0495_Left,
  Lateral: v2_0495_Lateral,
  QuadrantLeftLower: v2_0495_QuadrantLeftLower,
  Lower: v2_0495_Lower,
  QuadrantLeftUpper: v2_0495_QuadrantLeftUpper,
  Medial: v2_0495_Medial,
  Posterior: v2_0495_Posterior,
  Proximal: v2_0495_Proximal,
  Right: v2_0495_Right,
  QuadrantRightLower: v2_0495_QuadrantRightLower,
  QuadrantRightUpper: v2_0495_QuadrantRightUpper,
  Upper: v2_0495_Upper,
};
const v2_0496_ReleaseOfInformationMRAuthorizationToDisclosureProtectedHealthInformation: Coding = {
  code: '001',
  display: 'Release of Information/MR / Authorization to Disclosure Protected Health Information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_MedicalProcedureInvasive: Coding = {
  code: '002',
  display: 'Medical Procedure (invasive)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_AcknowledgeReceiptOfPrivacyNotice: Coding = {
  code: '003',
  display: 'Acknowledge Receipt of Privacy Notice',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Abortion: Coding = {
  code: '004',
  display: 'Abortion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_AbortionLaminaria: Coding = {
  code: '005',
  display: 'Abortion/Laminaria',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_AccutaneInformation: Coding = {
  code: '006',
  display: 'Accutane - Information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_AccutaneWoman: Coding = {
  code: '007',
  display: 'Accutane - Woman',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_AdvancedBeneficiaryNotice: Coding = {
  code: '008',
  display: 'Advanced Beneficiary Notice',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_AFPAlphaFetoproteinScreening: Coding = {
  code: '009',
  display: 'AFP (Alpha Fetoprotein) Screening',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_AmniocentesisConsentAndRefusal: Coding = {
  code: '010',
  display: 'Amniocentesis (consent & refusal)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_AnatomicalGiftOrganDonation: Coding = {
  code: '011',
  display: 'Anatomical Gift (organ donation)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_AnesthesiaComplications: Coding = {
  code: '012',
  display: 'Anesthesia - Complications',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_AnesthesiaQuestionnaire: Coding = {
  code: '013',
  display: 'Anesthesia - Questionnaire',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Angiogram: Coding = {
  code: '014',
  display: 'Angiogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Angioplasty: Coding = {
  code: '015',
  display: 'Angioplasty',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_AnticancerDrugs: Coding = {
  code: '016',
  display: 'Anticancer Drugs',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_AntipsychoticMedications: Coding = {
  code: '017',
  display: 'Antipsychotic Medications',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Arthrogram: Coding = {
  code: '018',
  display: 'Arthrogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Autopsy: Coding = {
  code: '019',
  display: 'Autopsy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_AZTTherapy: Coding = {
  code: '020',
  display: 'AZT Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_BiliaryDrainage: Coding = {
  code: '021',
  display: 'Biliary Drainage',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_BiliaryStoneExtraction: Coding = {
  code: '022',
  display: 'Biliary Stone Extraction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Biopsy: Coding = {
  code: '023',
  display: 'Biopsy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_BleedingTimeTest: Coding = {
  code: '024',
  display: 'Bleeding Time Test',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Bronchogram: Coding = {
  code: '025',
  display: 'Bronchogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_CardiacCatheterization: Coding = {
  code: '026',
  display: 'Cardiac Catheterization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_CoronaryAngiography: Coding = {
  code: '027',
  display: 'Coronary Angiography',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_CoronaryAngiographyWOSurgeryCapability: Coding = {
  code: '028',
  display: 'Coronary Angiography w/o Surgery Capability',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_CataractOpImplantOfFDAAprvdLens: Coding = {
  code: '029',
  display: 'Cataract Op/Implant of FDA Aprvd Lens',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_CataractOpImplantOfInvestigationalLens: Coding = {
  code: '030',
  display: 'Cataract Op/Implant of Investigational Lens',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_CataractSurgery: Coding = {
  code: '031',
  display: 'Cataract Surgery',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_CholeraImmunization: Coding = {
  code: '032',
  display: 'Cholera Immunization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_CholesterolScreening: Coding = {
  code: '033',
  display: 'Cholesterol Screening',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_CircumcisionNewborn: Coding = {
  code: '034',
  display: 'Circumcision - Newborn',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Colonoscopy: Coding = {
  code: '035',
  display: 'Colonoscopy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_ContactLenses: Coding = {
  code: '036',
  display: 'Contact Lenses',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_CTScanCervicalAndLumbar: Coding = {
  code: '037',
  display: 'CT Scan - Cervical & Lumbar',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_CTScanWIVContrastMediaIntoVein: Coding = {
  code: '038',
  display: 'CT Scan w/ IV Contrast Media into Vein',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_CVSChorionicVillusSampling: Coding = {
  code: '039',
  display: 'CVS (Chorionic Villus) Sampling',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Cystospy: Coding = {
  code: '040',
  display: 'Cystospy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_DisclosureOfProtectedHealthInformationToFamilyFriends: Coding = {
  code: '041',
  display: 'Disclosure of Protected Health Information to Family/Friends',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_DAndCAndConization: Coding = {
  code: '042',
  display: 'D & C and Conization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Dacryocystogram: Coding = {
  code: '043',
  display: 'Dacryocystogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_DiagnosticIsotope: Coding = {
  code: '044',
  display: 'Diagnostic Isotope',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_DrainageOfAnAbscess: Coding = {
  code: '045',
  display: 'Drainage of an Abscess',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_DrugScreening: Coding = {
  code: '046',
  display: 'Drug Screening',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_ElectronicMonitoringOfLaborRefusal: Coding = {
  code: '047',
  display: 'Electronic Monitoring of Labor - Refusal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_EndometrialBiopsy: Coding = {
  code: '048',
  display: 'Endometrial Biopsy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_EndoscopySclerosisOfEsophagealVarices: Coding = {
  code: '049',
  display: 'Endoscopy/Sclerosis of Esophageal Varices',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_ERCP: Coding = {
  code: '050',
  display: 'ERCP',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_ExposureToReportableCommunicableDisease: Coding = {
  code: '051',
  display: 'Exposure to reportable Communicable Disease',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_ExternalVersion: Coding = {
  code: '052',
  display: 'External Version',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_FluoresceinAngioscopy: Coding = {
  code: '053',
  display: 'Fluorescein Angioscopy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_HepatitisBConsentDeclination: Coding = {
  code: '054',
  display: 'Hepatitis B - Consent/Declination',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Herniogram: Coding = {
  code: '055',
  display: 'Herniogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_HIVTestConsentRefusal: Coding = {
  code: '056',
  display: 'HIV Test - Consent Refusal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_HIVTestDisclosure: Coding = {
  code: '057',
  display: 'HIV Test - Disclosure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_HIVTestPrenatal: Coding = {
  code: '058',
  display: 'HIV Test - Prenatal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_HomeIVTreatmentProgram: Coding = {
  code: '059',
  display: 'Home IV Treatment Program',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_HomeParenteralTreatmentProgram: Coding = {
  code: '060',
  display: 'Home Parenteral Treatment Program',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Hysterectomy: Coding = {
  code: '061',
  display: 'Hysterectomy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Hysterosalpingogram: Coding = {
  code: '062',
  display: 'Hysterosalpingogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_InjectionSlipConsent: Coding = {
  code: '063',
  display: 'Injection Slip/ Consent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_IntrauterineDevice: Coding = {
  code: '064',
  display: 'Intrauterine Device',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_IntrauterineDeviceSterilization: Coding = {
  code: '065',
  display: 'Intrauterine Device/Sterilization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_IntravascularInfusionOfStreptokinaseUrokinase: Coding = {
  code: '066',
  display: 'Intravascular Infusion of Streptokinase/Urokinase',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_IntravenousCholangiogram: Coding = {
  code: '067',
  display: 'Intravenous Cholangiogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_IntravenousDigitalAngiography: Coding = {
  code: '068',
  display: 'Intravenous Digital Angiography',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_IodineAdministration: Coding = {
  code: '069',
  display: 'Iodine Administration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_ISG: Coding = {
  code: '070',
  display: 'ISG',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_IVP: Coding = {
  code: '071',
  display: 'IVP',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_LaserPhotocoagulation: Coding = {
  code: '072',
  display: 'Laser Photocoagulation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_LaserTreatment: Coding = {
  code: '073',
  display: 'Laser treatment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_LithiumCarbonate: Coding = {
  code: '074',
  display: 'Lithium Carbonate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_LiverBiopsy: Coding = {
  code: '075',
  display: 'Liver Biopsy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_LumbarPuncture: Coding = {
  code: '076',
  display: 'Lumbar Puncture',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Lymphangiogram: Coding = {
  code: '077',
  display: 'Lymphangiogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_MAOInhibitors: Coding = {
  code: '078',
  display: 'MAO Inhibitors',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_MedPsychAndOrDrugAlcohol: Coding = {
  code: '079',
  display: 'Med, Psych, and/or Drug/Alcohol',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_MedicalTreatmentRefusal: Coding = {
  code: '080',
  display: 'Medical Treatment - Refusal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_MorningAfterPill: Coding = {
  code: '081',
  display: 'Morning-after Pill',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_MRIAdult: Coding = {
  code: '082',
  display: 'MRI - Adult',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_MRIPediatric: Coding = {
  code: '083',
  display: 'MRI - Pediatric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Myelogram: Coding = {
  code: '084',
  display: 'Myelogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_NeedleBiopsy: Coding = {
  code: '085',
  display: 'Needle Biopsy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_NeedleBiopsyOfLung: Coding = {
  code: '086',
  display: 'Needle Biopsy of Lung',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_NewbornTreatmentAndRelease: Coding = {
  code: '087',
  display: 'Newborn Treatment and Release',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_NorplantSubdermalBirthControlImplant: Coding = {
  code: '088',
  display: 'Norplant Subdermal Birth Control Implant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_OperationsAnesthesiaTransfusions: Coding = {
  code: '089',
  display: 'Operations, Anesthesia, Transfusions',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_OralContraceptives: Coding = {
  code: '090',
  display: 'Oral Contraceptives',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_OrganDonation: Coding = {
  code: '091',
  display: 'Organ Donation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_PatientPermitsConsents: Coding = {
  code: '092',
  display: 'Patient Permits, Consents',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_PatientTreatmentPermitReleaseAndAdmission: Coding = {
  code: '093',
  display: 'Patient Treatment Permit, Release & Admission',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_PenileInjections: Coding = {
  code: '094',
  display: 'Penile Injections',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_PercutaneousNephrostomy: Coding = {
  code: '095',
  display: 'Percutaneous Nephrostomy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_PercutaneousTranshepaticCholangiogram: Coding = {
  code: '096',
  display: 'Percutaneous Transhepatic Cholangiogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Photographs: Coding = {
  code: '097',
  display: 'Photographs',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_PhotographsEmployee: Coding = {
  code: '098',
  display: 'Photographs - Employee',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_PhotographsMedicalResearch: Coding = {
  code: '099',
  display: 'Photographs - Medical Research',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_PhotographsNewsMedia: Coding = {
  code: '100',
  display: 'Photographs - news Media',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_PsychiatricAdmissionNextOfKin: Coding = {
  code: '101',
  display: 'Psychiatric Admission - Next of Kin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_PsychiatricInformationDuringHospitalStay: Coding = {
  code: '102',
  display: 'Psychiatric Information During Hospital Stay',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_PublicReleaseOfInformation: Coding = {
  code: '103',
  display: 'Public Release of Information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_RadiologicProcedure: Coding = {
  code: '104',
  display: 'Radiologic Procedure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_RefusalOfTreatment: Coding = {
  code: '105',
  display: 'Refusal of Treatment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_ReleaseOfBody: Coding = {
  code: '106',
  display: 'Release of Body',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_ReleaseOfLimb: Coding = {
  code: '107',
  display: 'Release of Limb',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_RhImmuneGlobulin: Coding = {
  code: '108',
  display: 'Rh Immune Globulin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_RightsOfMedicalResearchParticipants: Coding = {
  code: '109',
  display: 'Rights of Medical Research Participants',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_RequestToRestrictAccessDisclosureToMedicalRecordProtectedHealthInformation: Coding = {
  code: '110',
  display: 'Request to Restrict Access/Disclosure to Medical Record/Protected Health Information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_RequestForRemainAnonymous: Coding = {
  code: '111',
  display: 'Request for Remain Anonymous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_SeatBeltExemption: Coding = {
  code: '112',
  display: 'Seat Belt Exemption',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Sialogram: Coding = {
  code: '113',
  display: 'Sialogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_VoidingCystogram: Coding = {
  code: '1137',
  display: 'Voiding Cystogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Sigmoidoscopy: Coding = {
  code: '114',
  display: 'Sigmoidoscopy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_SterilizationAnesthesiaAndMedicalServices: Coding = {
  code: '115',
  display: 'Sterilization - Anesthesia & Medical Services',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_SterilizationFederallyFunded: Coding = {
  code: '116',
  display: 'Sterilization -Federally Funded',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_SterilizationFemale: Coding = {
  code: '117',
  display: 'Sterilization - Female',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_SterilizationLaparoscopyPomeroy: Coding = {
  code: '118',
  display: 'Sterilization - Laparoscopy/Pomeroy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_SterilizationNonFederallyFunded: Coding = {
  code: '119',
  display: 'Sterilization - Non-Federally Funded',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_SterilizationSecondary: Coding = {
  code: '120',
  display: 'Sterilization - Secondary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Tranquilizers: Coding = {
  code: '121',
  display: 'Tranquilizers',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_TransferAcknowledgement: Coding = {
  code: '122',
  display: 'Transfer - Acknowledgement',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_TransferAuthorization: Coding = {
  code: '123',
  display: 'Transfer - Authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_TransferCertificationPhysician: Coding = {
  code: '124',
  display: 'Transfer Certification - Physician',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_TransferDischargeRequest: Coding = {
  code: '125',
  display: 'Transfer/Discharge Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_TransferForNonMedicalReasons: Coding = {
  code: '126',
  display: 'Transfer for Non-Medical Reasons',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_TransferInterfacultyNeonatal: Coding = {
  code: '127',
  display: 'Transfer - Interfaculty Neonatal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_TransferRefusal: Coding = {
  code: '128',
  display: 'Transfer Refusal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_TransferRefusalOfFurtherTreatment: Coding = {
  code: '129',
  display: 'Transfer Refusal of Further Treatment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_TreadmillAndEKG: Coding = {
  code: '130',
  display: 'Treadmill & EKG',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_TreadmillThallium201: Coding = {
  code: '131',
  display: 'Treadmill, Thallium-201',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Typhoid: Coding = {
  code: '132',
  display: 'Typhoid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_UseOfInvestigationalDevice: Coding = {
  code: '133',
  display: 'Use of Investigational Device',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_UseOfInvestigationalDrug: Coding = {
  code: '134',
  display: 'Use of Investigational Drug',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Venogram: Coding = {
  code: '135',
  display: 'Venogram',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
const v2_0496_Videotape: Coding = {
  code: '136',
  display: 'Videotape',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0496',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0496 ( Consent Type)
 */
export const V20496 = {
  ReleaseOfInformationMRAuthorizationToDisclosureProtectedHealthInformation: v2_0496_ReleaseOfInformationMRAuthorizationToDisclosureProtectedHealthInformation,
  MedicalProcedureInvasive: v2_0496_MedicalProcedureInvasive,
  AcknowledgeReceiptOfPrivacyNotice: v2_0496_AcknowledgeReceiptOfPrivacyNotice,
  Abortion: v2_0496_Abortion,
  AbortionLaminaria: v2_0496_AbortionLaminaria,
  AccutaneInformation: v2_0496_AccutaneInformation,
  AccutaneWoman: v2_0496_AccutaneWoman,
  AdvancedBeneficiaryNotice: v2_0496_AdvancedBeneficiaryNotice,
  AFPAlphaFetoproteinScreening: v2_0496_AFPAlphaFetoproteinScreening,
  AmniocentesisConsentAndRefusal: v2_0496_AmniocentesisConsentAndRefusal,
  AnatomicalGiftOrganDonation: v2_0496_AnatomicalGiftOrganDonation,
  AnesthesiaComplications: v2_0496_AnesthesiaComplications,
  AnesthesiaQuestionnaire: v2_0496_AnesthesiaQuestionnaire,
  Angiogram: v2_0496_Angiogram,
  Angioplasty: v2_0496_Angioplasty,
  AnticancerDrugs: v2_0496_AnticancerDrugs,
  AntipsychoticMedications: v2_0496_AntipsychoticMedications,
  Arthrogram: v2_0496_Arthrogram,
  Autopsy: v2_0496_Autopsy,
  AZTTherapy: v2_0496_AZTTherapy,
  BiliaryDrainage: v2_0496_BiliaryDrainage,
  BiliaryStoneExtraction: v2_0496_BiliaryStoneExtraction,
  Biopsy: v2_0496_Biopsy,
  BleedingTimeTest: v2_0496_BleedingTimeTest,
  Bronchogram: v2_0496_Bronchogram,
  CardiacCatheterization: v2_0496_CardiacCatheterization,
  CoronaryAngiography: v2_0496_CoronaryAngiography,
  CoronaryAngiographyWOSurgeryCapability: v2_0496_CoronaryAngiographyWOSurgeryCapability,
  CataractOpImplantOfFDAAprvdLens: v2_0496_CataractOpImplantOfFDAAprvdLens,
  CataractOpImplantOfInvestigationalLens: v2_0496_CataractOpImplantOfInvestigationalLens,
  CataractSurgery: v2_0496_CataractSurgery,
  CholeraImmunization: v2_0496_CholeraImmunization,
  CholesterolScreening: v2_0496_CholesterolScreening,
  CircumcisionNewborn: v2_0496_CircumcisionNewborn,
  Colonoscopy: v2_0496_Colonoscopy,
  ContactLenses: v2_0496_ContactLenses,
  CTScanCervicalAndLumbar: v2_0496_CTScanCervicalAndLumbar,
  CTScanWIVContrastMediaIntoVein: v2_0496_CTScanWIVContrastMediaIntoVein,
  CVSChorionicVillusSampling: v2_0496_CVSChorionicVillusSampling,
  Cystospy: v2_0496_Cystospy,
  DisclosureOfProtectedHealthInformationToFamilyFriends: v2_0496_DisclosureOfProtectedHealthInformationToFamilyFriends,
  DAndCAndConization: v2_0496_DAndCAndConization,
  Dacryocystogram: v2_0496_Dacryocystogram,
  DiagnosticIsotope: v2_0496_DiagnosticIsotope,
  DrainageOfAnAbscess: v2_0496_DrainageOfAnAbscess,
  DrugScreening: v2_0496_DrugScreening,
  ElectronicMonitoringOfLaborRefusal: v2_0496_ElectronicMonitoringOfLaborRefusal,
  EndometrialBiopsy: v2_0496_EndometrialBiopsy,
  EndoscopySclerosisOfEsophagealVarices: v2_0496_EndoscopySclerosisOfEsophagealVarices,
  ERCP: v2_0496_ERCP,
  ExposureToReportableCommunicableDisease: v2_0496_ExposureToReportableCommunicableDisease,
  ExternalVersion: v2_0496_ExternalVersion,
  FluoresceinAngioscopy: v2_0496_FluoresceinAngioscopy,
  HepatitisBConsentDeclination: v2_0496_HepatitisBConsentDeclination,
  Herniogram: v2_0496_Herniogram,
  HIVTestConsentRefusal: v2_0496_HIVTestConsentRefusal,
  HIVTestDisclosure: v2_0496_HIVTestDisclosure,
  HIVTestPrenatal: v2_0496_HIVTestPrenatal,
  HomeIVTreatmentProgram: v2_0496_HomeIVTreatmentProgram,
  HomeParenteralTreatmentProgram: v2_0496_HomeParenteralTreatmentProgram,
  Hysterectomy: v2_0496_Hysterectomy,
  Hysterosalpingogram: v2_0496_Hysterosalpingogram,
  InjectionSlipConsent: v2_0496_InjectionSlipConsent,
  IntrauterineDevice: v2_0496_IntrauterineDevice,
  IntrauterineDeviceSterilization: v2_0496_IntrauterineDeviceSterilization,
  IntravascularInfusionOfStreptokinaseUrokinase: v2_0496_IntravascularInfusionOfStreptokinaseUrokinase,
  IntravenousCholangiogram: v2_0496_IntravenousCholangiogram,
  IntravenousDigitalAngiography: v2_0496_IntravenousDigitalAngiography,
  IodineAdministration: v2_0496_IodineAdministration,
  ISG: v2_0496_ISG,
  IVP: v2_0496_IVP,
  LaserPhotocoagulation: v2_0496_LaserPhotocoagulation,
  LaserTreatment: v2_0496_LaserTreatment,
  LithiumCarbonate: v2_0496_LithiumCarbonate,
  LiverBiopsy: v2_0496_LiverBiopsy,
  LumbarPuncture: v2_0496_LumbarPuncture,
  Lymphangiogram: v2_0496_Lymphangiogram,
  MAOInhibitors: v2_0496_MAOInhibitors,
  MedPsychAndOrDrugAlcohol: v2_0496_MedPsychAndOrDrugAlcohol,
  MedicalTreatmentRefusal: v2_0496_MedicalTreatmentRefusal,
  MorningAfterPill: v2_0496_MorningAfterPill,
  MRIAdult: v2_0496_MRIAdult,
  MRIPediatric: v2_0496_MRIPediatric,
  Myelogram: v2_0496_Myelogram,
  NeedleBiopsy: v2_0496_NeedleBiopsy,
  NeedleBiopsyOfLung: v2_0496_NeedleBiopsyOfLung,
  NewbornTreatmentAndRelease: v2_0496_NewbornTreatmentAndRelease,
  NorplantSubdermalBirthControlImplant: v2_0496_NorplantSubdermalBirthControlImplant,
  OperationsAnesthesiaTransfusions: v2_0496_OperationsAnesthesiaTransfusions,
  OralContraceptives: v2_0496_OralContraceptives,
  OrganDonation: v2_0496_OrganDonation,
  PatientPermitsConsents: v2_0496_PatientPermitsConsents,
  PatientTreatmentPermitReleaseAndAdmission: v2_0496_PatientTreatmentPermitReleaseAndAdmission,
  PenileInjections: v2_0496_PenileInjections,
  PercutaneousNephrostomy: v2_0496_PercutaneousNephrostomy,
  PercutaneousTranshepaticCholangiogram: v2_0496_PercutaneousTranshepaticCholangiogram,
  Photographs: v2_0496_Photographs,
  PhotographsEmployee: v2_0496_PhotographsEmployee,
  PhotographsMedicalResearch: v2_0496_PhotographsMedicalResearch,
  PhotographsNewsMedia: v2_0496_PhotographsNewsMedia,
  PsychiatricAdmissionNextOfKin: v2_0496_PsychiatricAdmissionNextOfKin,
  PsychiatricInformationDuringHospitalStay: v2_0496_PsychiatricInformationDuringHospitalStay,
  PublicReleaseOfInformation: v2_0496_PublicReleaseOfInformation,
  RadiologicProcedure: v2_0496_RadiologicProcedure,
  RefusalOfTreatment: v2_0496_RefusalOfTreatment,
  ReleaseOfBody: v2_0496_ReleaseOfBody,
  ReleaseOfLimb: v2_0496_ReleaseOfLimb,
  RhImmuneGlobulin: v2_0496_RhImmuneGlobulin,
  RightsOfMedicalResearchParticipants: v2_0496_RightsOfMedicalResearchParticipants,
  RequestToRestrictAccessDisclosureToMedicalRecordProtectedHealthInformation: v2_0496_RequestToRestrictAccessDisclosureToMedicalRecordProtectedHealthInformation,
  RequestForRemainAnonymous: v2_0496_RequestForRemainAnonymous,
  SeatBeltExemption: v2_0496_SeatBeltExemption,
  Sialogram: v2_0496_Sialogram,
  VoidingCystogram: v2_0496_VoidingCystogram,
  Sigmoidoscopy: v2_0496_Sigmoidoscopy,
  SterilizationAnesthesiaAndMedicalServices: v2_0496_SterilizationAnesthesiaAndMedicalServices,
  SterilizationFederallyFunded: v2_0496_SterilizationFederallyFunded,
  SterilizationFemale: v2_0496_SterilizationFemale,
  SterilizationLaparoscopyPomeroy: v2_0496_SterilizationLaparoscopyPomeroy,
  SterilizationNonFederallyFunded: v2_0496_SterilizationNonFederallyFunded,
  SterilizationSecondary: v2_0496_SterilizationSecondary,
  Tranquilizers: v2_0496_Tranquilizers,
  TransferAcknowledgement: v2_0496_TransferAcknowledgement,
  TransferAuthorization: v2_0496_TransferAuthorization,
  TransferCertificationPhysician: v2_0496_TransferCertificationPhysician,
  TransferDischargeRequest: v2_0496_TransferDischargeRequest,
  TransferForNonMedicalReasons: v2_0496_TransferForNonMedicalReasons,
  TransferInterfacultyNeonatal: v2_0496_TransferInterfacultyNeonatal,
  TransferRefusal: v2_0496_TransferRefusal,
  TransferRefusalOfFurtherTreatment: v2_0496_TransferRefusalOfFurtherTreatment,
  TreadmillAndEKG: v2_0496_TreadmillAndEKG,
  TreadmillThallium201: v2_0496_TreadmillThallium201,
  Typhoid: v2_0496_Typhoid,
  UseOfInvestigationalDevice: v2_0496_UseOfInvestigationalDevice,
  UseOfInvestigationalDrug: v2_0496_UseOfInvestigationalDrug,
  Venogram: v2_0496_Venogram,
  Videotape: v2_0496_Videotape,
};
const v2_0497_Telephone: Coding = {
  code: 'T',
  display: 'Telephone',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0497',
};
const v2_0497_Verbal: Coding = {
  code: 'V',
  display: 'Verbal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0497',
};
const v2_0497_Written: Coding = {
  code: 'W',
  display: 'Written',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0497',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0497 ( Consent Mode)
 */
export const V20497 = {
  Telephone: v2_0497_Telephone,
  Verbal: v2_0497_Verbal,
  Written: v2_0497_Written,
};
const v2_0498_ActiveConsentHasBeenGranted: Coding = {
  code: 'A',
  display: 'Active - Consent has been granted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0498',
};
const v2_0498_BypassedConsentNotSought: Coding = {
  code: 'B',
  display: 'Bypassed (Consent not sought)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0498',
};
const v2_0498_LimitedConsentHasBeenGrantedWithLimitations: Coding = {
  code: 'L',
  display: 'Limited - Consent has been granted with limitations',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0498',
};
const v2_0498_PendingConsentHasNotYetBeenSought: Coding = {
  code: 'P',
  display: 'Pending - Consent has not yet been sought',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0498',
};
const v2_0498_RefusedConsentHasBeenRefused: Coding = {
  code: 'R',
  display: 'Refused - Consent has been refused',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0498',
};
const v2_0498_RescindedConsentWasInitiallyGrantedButWasSubsequentlyRevokedOrEnded: Coding = {
  code: 'X',
  display: 'Rescinded - Consent was initially granted, but was subsequently revoked or ended.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0498',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0498 ( Consent Status)
 */
export const V20498 = {
  ActiveConsentHasBeenGranted: v2_0498_ActiveConsentHasBeenGranted,
  BypassedConsentNotSought: v2_0498_BypassedConsentNotSought,
  LimitedConsentHasBeenGrantedWithLimitations: v2_0498_LimitedConsentHasBeenGrantedWithLimitations,
  PendingConsentHasNotYetBeenSought: v2_0498_PendingConsentHasNotYetBeenSought,
  RefusedConsentHasBeenRefused: v2_0498_RefusedConsentHasBeenRefused,
  RescindedConsentWasInitiallyGrantedButWasSubsequentlyRevokedOrEnded: v2_0498_RescindedConsentWasInitiallyGrantedButWasSubsequentlyRevokedOrEnded,
};
const v2_0499_Emergency: Coding = {
  code: 'E',
  display: 'Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0499',
};
const v2_0499_ProfessionalJudgment: Coding = {
  code: 'PJ',
  display: 'Professional Judgment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0499',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0499 ( Consent Bypass Reason)
 */
export const V20499 = {
  Emergency: v2_0499_Emergency,
  ProfessionalJudgment: v2_0499_ProfessionalJudgment,
};
const v2_0500_FullDisclosure: Coding = {
  code: 'F',
  display: 'Full Disclosure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0500',
};
const v2_0500_NoDisclosure: Coding = {
  code: 'N',
  display: 'No Disclosure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0500',
};
const v2_0500_PartialDisclosure: Coding = {
  code: 'P',
  display: 'Partial Disclosure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0500',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0500 ( Consent Disclosure Level)
 */
export const V20500 = {
  FullDisclosure: v2_0500_FullDisclosure,
  NoDisclosure: v2_0500_NoDisclosure,
  PartialDisclosure: v2_0500_PartialDisclosure,
};
const v2_0501_Emergency: Coding = {
  code: 'E',
  display: 'Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0501',
};
const v2_0501_PatientRequest: Coding = {
  code: 'PR',
  display: 'Patient Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0501',
};
const v2_0501_RxPrivate: Coding = {
  code: 'RX',
  display: 'Rx Private',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0501',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0501 ( Consent Non-Disclosure Reason)
 */
export const V20501 = {
  Emergency: v2_0501_Emergency,
  PatientRequest: v2_0501_PatientRequest,
  RxPrivate: v2_0501_RxPrivate,
};
const v2_0502_LegallyMandated: Coding = {
  code: 'LM',
  display: 'Legally mandated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0502',
};
const v2_0502_SubjectIsAMinor: Coding = {
  code: 'MIN',
  display: 'Subject is a minor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0502',
};
const v2_0502_SubjectIsNotCompetentToConsent: Coding = {
  code: 'NC',
  display: 'Subject is not competent to consent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0502',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0502 ( Non-Subject Consenter Reason)
 */
export const V20502 = {
  LegallyMandated: v2_0502_LegallyMandated,
  SubjectIsAMinor: v2_0502_SubjectIsAMinor,
  SubjectIsNotCompetentToConsent: v2_0502_SubjectIsNotCompetentToConsent,
};
const v2_0503_Cyclical: Coding = {
  code: 'C',
  display: 'Cyclical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0503',
};
const v2_0503_ReservedForFutureUse: Coding = {
  code: 'R',
  display: 'Reserved for future use',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0503',
};
const v2_0503_Sequential: Coding = {
  code: 'S',
  display: 'Sequential',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0503',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0503 ( Sequence/Results Flag)
 */
export const V20503 = {
  Cyclical: v2_0503_Cyclical,
  ReservedForFutureUse: v2_0503_ReservedForFutureUse,
  Sequential: v2_0503_Sequential,
};
const v2_0504_EndRelatedServiceRequestSEndCurrentServiceRequest: Coding = {
  code: 'EE',
  display: 'End related service request(s), end current service request.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0504',
};
const v2_0504_EndRelatedServiceRequestSStartCurrentServiceRequest: Coding = {
  code: 'ES',
  display: 'End related service request(s), start current service request.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0504',
};
const v2_0504_StartRelatedServiceRequestSEndCurrentServiceRequest: Coding = {
  code: 'SE',
  display: 'Start related service request(s), end current service request.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0504',
};
const v2_0504_StartRelatedServiceRequestSStartCurrentServiceRequest: Coding = {
  code: 'SS',
  display: 'Start related service request(s), start current service request.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0504',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0504 ( Sequence Condition Code)
 */
export const V20504 = {
  EndRelatedServiceRequestSEndCurrentServiceRequest: v2_0504_EndRelatedServiceRequestSEndCurrentServiceRequest,
  EndRelatedServiceRequestSStartCurrentServiceRequest: v2_0504_EndRelatedServiceRequestSStartCurrentServiceRequest,
  StartRelatedServiceRequestSEndCurrentServiceRequest: v2_0504_StartRelatedServiceRequestSEndCurrentServiceRequest,
  StartRelatedServiceRequestSStartCurrentServiceRequest: v2_0504_StartRelatedServiceRequestSStartCurrentServiceRequest,
};
const v2_0505_TheLastServiceRequestInACyclicGroup: Coding = {
  code: '#',
  display: 'The last service request in a cyclic group.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0505',
};
const v2_0505_TheFirstServiceRequestInACyclicGroup: Coding = {
  code: '*',
  display: 'The first service request in a cyclic group',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0505',
};
const v2_0505_FirstService: Coding = {
  code: 'F',
  display: 'First service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0505',
};
const v2_0505_LastService: Coding = {
  code: 'L',
  display: 'Last service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0505',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0505 ( Cyclic Entry/Exit Indicator)
 */
export const V20505 = {
  TheLastServiceRequestInACyclicGroup: v2_0505_TheLastServiceRequestInACyclicGroup,
  TheFirstServiceRequestInACyclicGroup: v2_0505_TheFirstServiceRequestInACyclicGroup,
  FirstService: v2_0505_FirstService,
  LastService: v2_0505_LastService,
};
const v2_0506_Compound: Coding = {
  code: 'C',
  display: 'Compound',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0506',
};
const v2_0506_Exclusive: Coding = {
  code: 'E',
  display: 'Exclusive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0506',
};
const v2_0506_NursePrerogative: Coding = {
  code: 'N',
  display: 'Nurse prerogative',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0506',
};
const v2_0506_Simultaneous: Coding = {
  code: 'S',
  display: 'Simultaneous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0506',
};
const v2_0506_Tapering: Coding = {
  code: 'T',
  display: 'Tapering',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0506',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0506 ( Service Request Relationship)
 */
export const V20506 = {
  Compound: v2_0506_Compound,
  Exclusive: v2_0506_Exclusive,
  NursePrerogative: v2_0506_NursePrerogative,
  Simultaneous: v2_0506_Simultaneous,
  Tapering: v2_0506_Tapering,
};
const v2_0507_AlertProviderWhenAbnormal: Coding = {
  code: 'A',
  display: 'Alert provider when abnormal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0507',
};
const v2_0507_BlindCopy: Coding = {
  code: 'BCC',
  display: 'Blind copy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0507',
};
const v2_0507_CopiesRequested: Coding = {
  code: 'CC',
  display: 'Copies requested',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0507',
};
const v2_0507_FilmWithPatient: Coding = {
  code: 'F',
  display: 'Film-with-patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0507',
};
const v2_0507_NotifyProviderWhenReady: Coding = {
  code: 'N',
  display: 'Notify provider when ready',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0507',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0507 ( Observation Result Handling)
 */
export const V20507 = {
  AlertProviderWhenAbnormal: v2_0507_AlertProviderWhenAbnormal,
  BlindCopy: v2_0507_BlindCopy,
  CopiesRequested: v2_0507_CopiesRequested,
  FilmWithPatient: v2_0507_FilmWithPatient,
  NotifyProviderWhenReady: v2_0507_NotifyProviderWhenReady,
};
const v2_0508_AutologousUnit: Coding = {
  code: 'AU',
  display: 'Autologous Unit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0508',
};
const v2_0508_CMVNegative: Coding = {
  code: 'CM',
  display: 'CMV Negative',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0508',
};
const v2_0508_CMVSafe: Coding = {
  code: 'CS',
  display: 'CMV Safe',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0508',
};
const v2_0508_DirectedUnit: Coding = {
  code: 'DI',
  display: 'Directed Unit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0508',
};
const v2_0508_FreshUnit: Coding = {
  code: 'FR',
  display: 'Fresh unit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0508',
};
const v2_0508_HemoglobinSNegative: Coding = {
  code: 'HB',
  display: 'Hemoglobin S Negative',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0508',
};
const v2_0508_HLAMatched: Coding = {
  code: 'HL',
  display: 'HLA Matched',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0508',
};
const v2_0508_IgADeficient: Coding = {
  code: 'IG',
  display: 'IgA Deficient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0508',
};
const v2_0508_Irradiated: Coding = {
  code: 'IR',
  display: 'Irradiated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0508',
};
const v2_0508_Leukoreduced: Coding = {
  code: 'LR',
  display: 'Leukoreduced',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0508',
};
const v2_0508_Washed: Coding = {
  code: 'WA',
  display: 'Washed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0508',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0508 ( Blood Product Processing Requirements)
 */
export const V20508 = {
  AutologousUnit: v2_0508_AutologousUnit,
  CMVNegative: v2_0508_CMVNegative,
  CMVSafe: v2_0508_CMVSafe,
  DirectedUnit: v2_0508_DirectedUnit,
  FreshUnit: v2_0508_FreshUnit,
  HemoglobinSNegative: v2_0508_HemoglobinSNegative,
  HLAMatched: v2_0508_HLAMatched,
  IgADeficient: v2_0508_IgADeficient,
  Irradiated: v2_0508_Irradiated,
  Leukoreduced: v2_0508_Leukoreduced,
  Washed: v2_0508_Washed,
};
const v2_0510_ReleasedIntoInventoryForGeneralAvailability: Coding = {
  code: 'CR',
  display: 'Released into inventory for general availability',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0510',
};
const v2_0510_DispensedToPatientLocation: Coding = {
  code: 'DS',
  display: 'Dispensed to patient location',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0510',
};
const v2_0510_PresumedTransfusedDispensedAndNotReturned: Coding = {
  code: 'PT',
  display: 'Presumed transfused (dispensed and not returned)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0510',
};
const v2_0510_ReturnedUnusedNoLongerNeeded: Coding = {
  code: 'RA',
  display: 'Returned unused/no longer needed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0510',
};
const v2_0510_ReservedAndReadyToDispense: Coding = {
  code: 'RD',
  display: 'Reserved and ready to dispense',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0510',
};
const v2_0510_ReleasedNoLongerAllocatedForThePatient: Coding = {
  code: 'RE',
  display: 'Released (no longer allocated for the patient)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0510',
};
const v2_0510_ReceivedIntoInventoryForSpecifiedPatient: Coding = {
  code: 'RI',
  display: 'Received into inventory (for specified patient)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0510',
};
const v2_0510_ReturnedUnusedKeepLinkedToPatientForPossibleUseLater: Coding = {
  code: 'RL',
  display: 'Returned unused/keep linked to patient for possible use later',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0510',
};
const v2_0510_RequestToDispenseBloodProduct: Coding = {
  code: 'RQ',
  display: 'Request to dispense blood product',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0510',
};
const v2_0510_ReservedOrderedAndProductAllocatedForThePatient: Coding = {
  code: 'RS',
  display: 'Reserved (ordered and product allocated for the patient)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0510',
};
const v2_0510_WastedProductNoLongerViable: Coding = {
  code: 'WA',
  display: 'Wasted (product no longer viable)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0510',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0510 ( Blood Product Dispense Status)
 */
export const V20510 = {
  ReleasedIntoInventoryForGeneralAvailability: v2_0510_ReleasedIntoInventoryForGeneralAvailability,
  DispensedToPatientLocation: v2_0510_DispensedToPatientLocation,
  PresumedTransfusedDispensedAndNotReturned: v2_0510_PresumedTransfusedDispensedAndNotReturned,
  ReturnedUnusedNoLongerNeeded: v2_0510_ReturnedUnusedNoLongerNeeded,
  ReservedAndReadyToDispense: v2_0510_ReservedAndReadyToDispense,
  ReleasedNoLongerAllocatedForThePatient: v2_0510_ReleasedNoLongerAllocatedForThePatient,
  ReceivedIntoInventoryForSpecifiedPatient: v2_0510_ReceivedIntoInventoryForSpecifiedPatient,
  ReturnedUnusedKeepLinkedToPatientForPossibleUseLater: v2_0510_ReturnedUnusedKeepLinkedToPatientForPossibleUseLater,
  RequestToDispenseBloodProduct: v2_0510_RequestToDispenseBloodProduct,
  ReservedOrderedAndProductAllocatedForThePatient: v2_0510_ReservedOrderedAndProductAllocatedForThePatient,
  WastedProductNoLongerViable: v2_0510_WastedProductNoLongerViable,
};
const v2_0511_RecordComingOverIsACorrectionAndThusReplacesAFinalStatus: Coding = {
  code: 'C',
  display: 'Record coming over is a correction and thus replaces a final status',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0511',
};
const v2_0511_DeletesTheBPXRecord: Coding = {
  code: 'D',
  display: 'Deletes the BPX record',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0511',
};
const v2_0511_FinalStatusCanOnlyBeChangedWithACorrectedStatus: Coding = {
  code: 'F',
  display: 'Final status; Can only be changed with a corrected status',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0511',
};
const v2_0511_OrderDetailDescriptionOnlyNoStatus: Coding = {
  code: 'O',
  display: 'Order detail description only (no status)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0511',
};
const v2_0511_PreliminaryStatus: Coding = {
  code: 'P',
  display: 'Preliminary status',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0511',
};
const v2_0511_PostOriginalAsWrongEGTransmittedForWrongPatient: Coding = {
  code: 'W',
  display: 'Post original as wrong, e.g., transmitted for wrong patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0511',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0511 ( BP Observation Status Codes Interpretation)
 */
export const V20511 = {
  RecordComingOverIsACorrectionAndThusReplacesAFinalStatus: v2_0511_RecordComingOverIsACorrectionAndThusReplacesAFinalStatus,
  DeletesTheBPXRecord: v2_0511_DeletesTheBPXRecord,
  FinalStatusCanOnlyBeChangedWithACorrectedStatus: v2_0511_FinalStatusCanOnlyBeChangedWithACorrectedStatus,
  OrderDetailDescriptionOnlyNoStatus: v2_0511_OrderDetailDescriptionOnlyNoStatus,
  PreliminaryStatus: v2_0511_PreliminaryStatus,
  PostOriginalAsWrongEGTransmittedForWrongPatient: v2_0511_PostOriginalAsWrongEGTransmittedForWrongPatient,
};
const v2_0513_ReturnedUnusedNoLongerNeeded: Coding = {
  code: 'RA',
  display: 'Returned unused/no longer needed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0513',
};
const v2_0513_ReturnedUnusedKeepLinkedToPatientForPossibleUseLater: Coding = {
  code: 'RL',
  display: 'Returned unused/keep linked to patient for possible use later',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0513',
};
const v2_0513_TransfusedWithAdverseReaction: Coding = {
  code: 'TR',
  display: 'Transfused with adverse reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0513',
};
const v2_0513_Transfused: Coding = {
  code: 'TX',
  display: 'Transfused',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0513',
};
const v2_0513_WastedProductNoLongerViable: Coding = {
  code: 'WA',
  display: 'Wasted (product no longer viable)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0513',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0513 ( Blood Product Transfusion/Disposition Status)
 */
export const V20513 = {
  ReturnedUnusedNoLongerNeeded: v2_0513_ReturnedUnusedNoLongerNeeded,
  ReturnedUnusedKeepLinkedToPatientForPossibleUseLater: v2_0513_ReturnedUnusedKeepLinkedToPatientForPossibleUseLater,
  TransfusedWithAdverseReaction: v2_0513_TransfusedWithAdverseReaction,
  Transfused: v2_0513_Transfused,
  WastedProductNoLongerViable: v2_0513_WastedProductNoLongerViable,
};
const v2_0514_ABOIncompatibleTransfusionReaction: Coding = {
  code: 'ABOINC',
  display: 'ABO Incompatible Transfusion Reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_AcuteHemolyticTransfusionReaction: Coding = {
  code: 'ACUTHEHTR',
  display: 'Acute Hemolytic Transfusion Reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_AllergicReactionFirst: Coding = {
  code: 'ALLERGIC1',
  display: 'Allergic Reaction - First',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_AllergicReactionRecurrent: Coding = {
  code: 'ALLERGIC2',
  display: 'Allergic Reaction - Recurrent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_AllergicReactionRepeating: Coding = {
  code: 'ALLERGICR',
  display: 'Allergic Reaction - Repeating',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_AnaphylacticReaction: Coding = {
  code: 'ANAPHYLAC',
  display: 'Anaphylactic Reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_ReactionToBacterialContamination: Coding = {
  code: 'BACTCONTAM',
  display: 'Reaction to Bacterial Contamination',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_DelayedHemolyticTransfusionReaction: Coding = {
  code: 'DELAYEDHTR',
  display: 'Delayed Hemolytic Transfusion Reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_DelayedSerologicalTransfusionReaction: Coding = {
  code: 'DELAYEDSTR',
  display: 'Delayed Serological Transfusion Reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_GraftVsHostDiseaseTransfusionAssociated: Coding = {
  code: 'GVHD',
  display: 'Graft vs Host Disease - Transfusion - Associated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_NonHemolyticHypotensiveReaction: Coding = {
  code: 'HYPOTENS',
  display: 'Non-hemolytic Hypotensive Reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_NonHemolyticFeverChillTransfusionReactionFirst: Coding = {
  code: 'NONHTR1',
  display: 'Non-Hemolytic Fever Chill Transfusion Reaction - First',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_NonHemolyticFeverChillTransfusionReactionRecurrent: Coding = {
  code: 'NONHTR2',
  display: 'Non-Hemolytic Fever Chill Transfusion Reaction - Recurrent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_NonHemolyticFeverChillTransfusionReactionRepeating: Coding = {
  code: 'NONHTRREC',
  display: 'Non-Hemolytic Fever Chill Transfusion Reaction - Repeating',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_NonImmuneHemolysis: Coding = {
  code: 'NONIMMUNE',
  display: 'Non-Immune Hemolysis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_NonSpecificNonHemolyticTransfusionReaction: Coding = {
  code: 'NONSPEC',
  display: 'Non-Specific, Non-Hemolytic Transfusion Reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_NoEvidenceOfTransfusionReaction: Coding = {
  code: 'NORXN',
  display: 'No Evidence of Transfusion Reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_PosttransfusionPurpura: Coding = {
  code: 'PTP',
  display: 'Posttransfusion Purpura',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
const v2_0514_SymptomsMostLikelyDueToVolumeOverload: Coding = {
  code: 'VOLOVER',
  display: 'Symptoms most likely due to volume overload',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0514',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0514 ( Transfusion Adverse Reaction)
 */
export const V20514 = {
  ABOIncompatibleTransfusionReaction: v2_0514_ABOIncompatibleTransfusionReaction,
  AcuteHemolyticTransfusionReaction: v2_0514_AcuteHemolyticTransfusionReaction,
  AllergicReactionFirst: v2_0514_AllergicReactionFirst,
  AllergicReactionRecurrent: v2_0514_AllergicReactionRecurrent,
  AllergicReactionRepeating: v2_0514_AllergicReactionRepeating,
  AnaphylacticReaction: v2_0514_AnaphylacticReaction,
  ReactionToBacterialContamination: v2_0514_ReactionToBacterialContamination,
  DelayedHemolyticTransfusionReaction: v2_0514_DelayedHemolyticTransfusionReaction,
  DelayedSerologicalTransfusionReaction: v2_0514_DelayedSerologicalTransfusionReaction,
  GraftVsHostDiseaseTransfusionAssociated: v2_0514_GraftVsHostDiseaseTransfusionAssociated,
  NonHemolyticHypotensiveReaction: v2_0514_NonHemolyticHypotensiveReaction,
  NonHemolyticFeverChillTransfusionReactionFirst: v2_0514_NonHemolyticFeverChillTransfusionReactionFirst,
  NonHemolyticFeverChillTransfusionReactionRecurrent: v2_0514_NonHemolyticFeverChillTransfusionReactionRecurrent,
  NonHemolyticFeverChillTransfusionReactionRepeating: v2_0514_NonHemolyticFeverChillTransfusionReactionRepeating,
  NonImmuneHemolysis: v2_0514_NonImmuneHemolysis,
  NonSpecificNonHemolyticTransfusionReaction: v2_0514_NonSpecificNonHemolyticTransfusionReaction,
  NoEvidenceOfTransfusionReaction: v2_0514_NoEvidenceOfTransfusionReaction,
  PosttransfusionPurpura: v2_0514_PosttransfusionPurpura,
  SymptomsMostLikelyDueToVolumeOverload: v2_0514_SymptomsMostLikelyDueToVolumeOverload,
};
const v2_0516_Error: Coding = {
  code: 'E',
  display: 'Error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0516',
};
const v2_0516_FatalError: Coding = {
  code: 'F',
  display: 'Fatal Error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0516',
};
const v2_0516_Information: Coding = {
  code: 'I',
  display: 'Information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0516',
};
const v2_0516_Warning: Coding = {
  code: 'W',
  display: 'Warning',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0516',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0516 ( Error severity)
 */
export const V20516 = {
  Error: v2_0516_Error,
  FatalError: v2_0516_FatalError,
  Information: v2_0516_Information,
  Warning: v2_0516_Warning,
};
const v2_0517_InformHelpDesk: Coding = {
  code: 'HD',
  display: 'Inform help desk',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0517',
};
const v2_0517_DoNOTInformPatient: Coding = {
  code: 'NPAT',
  display: 'Do NOT inform patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0517',
};
const v2_0517_InformPatient: Coding = {
  code: 'PAT',
  display: 'Inform patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0517',
};
const v2_0517_InformUser: Coding = {
  code: 'USR',
  display: 'Inform User',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0517',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0517 ( Inform person code)
 */
export const V20517 = {
  InformHelpDesk: v2_0517_InformHelpDesk,
  DoNOTInformPatient: v2_0517_DoNOTInformPatient,
  InformPatient: v2_0517_InformPatient,
  InformUser: v2_0517_InformUser,
};
const v2_0518_EquivalenceOverride: Coding = {
  code: 'EQV',
  display: 'Equivalence Override',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0518',
};
const v2_0518_ExtensionOverride: Coding = {
  code: 'EXTN',
  display: 'Extension Override',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0518',
};
const v2_0518_IntervalOverride: Coding = {
  code: 'INLV',
  display: 'Interval Override',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0518',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0518 ( Override type)
 */
export const V20518 = {
  EquivalenceOverride: v2_0518_EquivalenceOverride,
  ExtensionOverride: v2_0518_ExtensionOverride,
  IntervalOverride: v2_0518_IntervalOverride,
};
const v2_0520_High: Coding = {
  code: 'H',
  display: 'High',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0520',
};
const v2_0520_Low: Coding = {
  code: 'L',
  display: 'Low',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0520',
};
const v2_0520_Medium: Coding = {
  code: 'M',
  display: 'Medium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0520',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0520 ( Message waiting priority)
 */
export const V20520 = {
  High: v2_0520_High,
  Low: v2_0520_Low,
  Medium: v2_0520_Medium,
};
const v2_0523_IndicatesAPercentChange: Coding = {
  code: '%',
  display: 'Indicates a percent change',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0523',
};
const v2_0523_AbsoluteChange: Coding = {
  code: 'a',
  display: 'Absolute Change',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0523',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0523 ( Computation type)
 */
export const V20523 = {
  IndicatesAPercentChange: v2_0523_IndicatesAPercentChange,
  AbsoluteChange: v2_0523_AbsoluteChange,
};
const v2_0527_DayOfTheMonth: Coding = {
  code: 'DM',
  display: 'day of the month',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0527',
};
const v2_0527_DayOfTheWeekBeginsWithMonday: Coding = {
  code: 'DW',
  display: 'day of the week (begins with Monday)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0527',
};
const v2_0527_DayOfTheYear: Coding = {
  code: 'DY',
  display: 'day of the year',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0527',
};
const v2_0527_HourOfTheDay: Coding = {
  code: 'HD',
  display: 'hour of the day',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0527',
};
const v2_0527_MonthOfTheYear: Coding = {
  code: 'MY',
  display: 'month of the year',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0527',
};
const v2_0527_MinuteOfTheHour: Coding = {
  code: 'NH',
  display: 'minute of the hour',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0527',
};
const v2_0527_SecondOfTheMinute: Coding = {
  code: 'SN',
  display: 'second of the minute',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0527',
};
const v2_0527_WeekOfTheYear: Coding = {
  code: 'WY',
  display: 'week of the year',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0527',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0527 ( Calendar alignment)
 */
export const V20527 = {
  DayOfTheMonth: v2_0527_DayOfTheMonth,
  DayOfTheWeekBeginsWithMonday: v2_0527_DayOfTheWeekBeginsWithMonday,
  DayOfTheYear: v2_0527_DayOfTheYear,
  HourOfTheDay: v2_0527_HourOfTheDay,
  MonthOfTheYear: v2_0527_MonthOfTheYear,
  MinuteOfTheHour: v2_0527_MinuteOfTheHour,
  SecondOfTheMinute: v2_0527_SecondOfTheMinute,
  WeekOfTheYear: v2_0527_WeekOfTheYear,
};
const v2_0528_BeforeMealFromLatAnteCibus: Coding = {
  code: 'AC',
  display: 'before meal (from lat. ante cibus)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0528',
};
const v2_0528_BeforeLunchFromLatAnteCibusDiurnus: Coding = {
  code: 'ACD',
  display: 'before lunch (from lat. ante cibus diurnus)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0528',
};
const v2_0528_BeforeBreakfastFromLatAnteCibusMatutinus: Coding = {
  code: 'ACM',
  display: 'before breakfast (from lat. ante cibus matutinus)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0528',
};
const v2_0528_BeforeDinnerFromLatAnteCibusVespertinus: Coding = {
  code: 'ACV',
  display: 'before dinner (from lat. ante cibus vespertinus)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0528',
};
const v2_0528_TheHourOfSleepEGH1822: Coding = {
  code: 'HS',
  display: 'the hour of sleep (e.g., H18-22)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0528',
};
const v2_0528_BetweenMealsFromLatInterCibus: Coding = {
  code: 'IC',
  display: 'between meals (from lat. inter cibus)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0528',
};
const v2_0528_BetweenLunchAndDinner: Coding = {
  code: 'ICD',
  display: 'between lunch and dinner',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0528',
};
const v2_0528_BetweenBreakfastAndLunch: Coding = {
  code: 'ICM',
  display: 'between breakfast and lunch',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0528',
};
const v2_0528_BetweenDinnerAndTheHourOfSleep: Coding = {
  code: 'ICV',
  display: 'between dinner and the hour of sleep',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0528',
};
const v2_0528_AfterMealFromLatPostCibus: Coding = {
  code: 'PC',
  display: 'after meal (from lat. post cibus)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0528',
};
const v2_0528_AfterLunchFromLatPostCibusDiurnus: Coding = {
  code: 'PCD',
  display: 'after lunch (from lat. post cibus diurnus)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0528',
};
const v2_0528_AfterBreakfastFromLatPostCibusMatutinus: Coding = {
  code: 'PCM',
  display: 'after breakfast (from lat. post cibus matutinus)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0528',
};
const v2_0528_AfterDinnerFromLatPostCibusVespertinus: Coding = {
  code: 'PCV',
  display: 'after dinner (from lat. post cibus vespertinus)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0528',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0528 ( Event related period)
 */
export const V20528 = {
  BeforeMealFromLatAnteCibus: v2_0528_BeforeMealFromLatAnteCibus,
  BeforeLunchFromLatAnteCibusDiurnus: v2_0528_BeforeLunchFromLatAnteCibusDiurnus,
  BeforeBreakfastFromLatAnteCibusMatutinus: v2_0528_BeforeBreakfastFromLatAnteCibusMatutinus,
  BeforeDinnerFromLatAnteCibusVespertinus: v2_0528_BeforeDinnerFromLatAnteCibusVespertinus,
  TheHourOfSleepEGH1822: v2_0528_TheHourOfSleepEGH1822,
  BetweenMealsFromLatInterCibus: v2_0528_BetweenMealsFromLatInterCibus,
  BetweenLunchAndDinner: v2_0528_BetweenLunchAndDinner,
  BetweenBreakfastAndLunch: v2_0528_BetweenBreakfastAndLunch,
  BetweenDinnerAndTheHourOfSleep: v2_0528_BetweenDinnerAndTheHourOfSleep,
  AfterMealFromLatPostCibus: v2_0528_AfterMealFromLatPostCibus,
  AfterLunchFromLatPostCibusDiurnus: v2_0528_AfterLunchFromLatPostCibusDiurnus,
  AfterBreakfastFromLatPostCibusMatutinus: v2_0528_AfterBreakfastFromLatPostCibusMatutinus,
  AfterDinnerFromLatPostCibusVespertinus: v2_0528_AfterDinnerFromLatPostCibusVespertinus,
};
const v2_0530_AmericanExpress: Coding = {
  code: 'AE',
  display: 'American Express',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0530',
};
const v2_0530_DrugEnforcementAgency: Coding = {
  code: 'DEA',
  display: 'Drug Enforcement Agency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0530',
};
const v2_0530_DepartmentOfDefense: Coding = {
  code: 'DOD',
  display: 'Department of Defense',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0530',
};
const v2_0530_MasterCard: Coding = {
  code: 'MC',
  display: 'Master Card',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0530',
};
const v2_0530_VeteransAffairs: Coding = {
  code: 'VA',
  display: 'Veterans Affairs',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0530',
};
const v2_0530_Visa: Coding = {
  code: 'VI',
  display: 'Visa',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0530',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0530 ( Organization, agency, department)
 */
export const V20530 = {
  AmericanExpress: v2_0530_AmericanExpress,
  DrugEnforcementAgency: v2_0530_DrugEnforcementAgency,
  DepartmentOfDefense: v2_0530_DepartmentOfDefense,
  MasterCard: v2_0530_MasterCard,
  VeteransAffairs: v2_0530_VeteransAffairs,
  Visa: v2_0530_Visa,
};
const v3_NullFlavor_AskedButUnknown: Coding = {
  code: 'ASKU',
  display: 'asked but unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_NullFlavor_NoInformation: Coding = {
  code: 'NI',
  display: 'NoInformation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_NullFlavor_NotPresent: Coding = {
  code: 'NP',
  display: 'not present',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0532 ( Expanded yes/no indicator)
 */
export const V20532 = {
  /**
   * Information was sought but not found (e.g., patient was asked but didn't know)
   */
  AskedButUnknown_v3_NullFlavor: v3_NullFlavor_AskedButUnknown,
  No_v2_0136: v2_0136_No,
  /**
   * Known to have no proper value (e.g., last menstrual period for a male).
   */
  NotApplicable_v3_NullFlavor: v3_NullFlavor_NotApplicable,
  /**
   * This information has not been sought (e.g., patient was not asked)
   */
  NotAsked_v3_NullFlavor: v3_NullFlavor_NotAsked,
  /**
   * Information is not available at this time but it is expected that it will be available later.
   */
  TemporarilyUnavailable_v3_NullFlavor: v3_NullFlavor_TemporarilyUnavailable,
  /**
   * Description:The value is exceptional (missing, omitted, incomplete, improper). No information as to the reason for being an exceptional value is provided. This is the most general exceptional value. It is also the default exceptional value.
   */
  NoInformation_v3_NullFlavor: v3_NullFlavor_NoInformation,
  /**
   * Value is not present in a message.  This is only defined in messages, never in application data!  All values not present in the message must be replaced by the applicable default, or no-information (NI) as the default of all defaults.
   */
  NotPresent_v3_NullFlavor: v3_NullFlavor_NotPresent,
  /**
   * Description:A proper value is applicable, but not known.
   *
   *
   *                            Usage Notes: This means the actual value is not known.  If the only thing that is unknown is how to properly express the value in the necessary constraints (value set, datatype, etc.), then the OTH or UNC flavor should be used.  No properties should be included for a datatype with this property unless:
   *
   *
   *                            Those properties themselves directly translate to a semantic of "unknown".  (E.g. a local code sent as a translation that conveys 'unknown')
   *                            Those properties further qualify the nature of what is unknown.  (E.g. specifying a use code of "H" and a URL prefix of "tel:" to convey that it is the home phone number that is unknown.)
   */
  Unknown_v3_NullFlavor: v3_NullFlavor_Unknown,
  Yes_v2_0136: v2_0136_Yes,
};
const v2_0534_LastRitesOnly: Coding = {
  code: 'L',
  display: 'Last Rites only',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0534',
};
const v2_0534_No: Coding = {
  code: 'N',
  display: 'No',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0534',
};
const v2_0534_Other: Coding = {
  code: 'O',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0534',
};
const v2_0534_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0534',
};
const v2_0534_Yes: Coding = {
  code: 'Y',
  display: 'Yes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0534',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0534 ( Notify Clergy Code)
 */
export const V20534 = {
  LastRitesOnly: v2_0534_LastRitesOnly,
  No: v2_0534_No,
  Other: v2_0534_Other,
  Unknown: v2_0534_Unknown,
  Yes: v2_0534_Yes,
};
const v2_0535_SignedCMS1500ClaimFormOnFileEGAuthorizationForReleaseOfAnyMedicalOrOtherInformationNecessaryToProcessThisClaimAndAssignmentOfBenefits: Coding = {
  code: 'C',
  display:
    'Signed CMS-1500 claim form on file, e.g., authorization for release of any medical or other information necessary to process this claim and assignment of benefits.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0535',
};
const v2_0535_SignedAuthorizationForAssignmentOfBenefitsOnFile: Coding = {
  code: 'M',
  display: 'Signed authorization for assignment of benefits on file.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0535',
};
const v2_0535_SignatureGeneratedByProviderBecauseThePatientWasNotPhysicallyPresentForServices: Coding = {
  code: 'P',
  display: 'Signature generated by provider because the patient was not physically present for services.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0535',
};
const v2_0535_SignedAuthorizationForReleaseOfAnyMedicalOrOtherInformationNecessaryToProcessThisClaimOnFile: Coding = {
  code: 'S',
  display: 'Signed authorization for release of any medical or other information necessary to process this claim on file.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0535',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0535 ( Signature Code)
 */
export const V20535 = {
  SignedCMS1500ClaimFormOnFileEGAuthorizationForReleaseOfAnyMedicalOrOtherInformationNecessaryToProcessThisClaimAndAssignmentOfBenefits: v2_0535_SignedCMS1500ClaimFormOnFileEGAuthorizationForReleaseOfAnyMedicalOrOtherInformationNecessaryToProcessThisClaimAndAssignmentOfBenefits,
  SignedAuthorizationForAssignmentOfBenefitsOnFile: v2_0535_SignedAuthorizationForAssignmentOfBenefitsOnFile,
  SignatureGeneratedByProviderBecauseThePatientWasNotPhysicallyPresentForServices: v2_0535_SignatureGeneratedByProviderBecauseThePatientWasNotPhysicallyPresentForServices,
  SignedAuthorizationForReleaseOfAnyMedicalOrOtherInformationNecessaryToProcessThisClaimOnFile: v2_0535_SignedAuthorizationForReleaseOfAnyMedicalOrOtherInformationNecessaryToProcessThisClaimOnFile,
};
const v2_0536_Expired: Coding = {
  code: 'E',
  display: 'Expired',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0536',
};
const v2_0536_Inactive: Coding = {
  code: 'I',
  display: 'Inactive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0536',
};
const v2_0536_Provisional: Coding = {
  code: 'P',
  display: 'Provisional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0536',
};
const v2_0536_Revoked: Coding = {
  code: 'R',
  display: 'Revoked',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0536',
};
const v2_0536_ActiveValid: Coding = {
  code: 'V',
  display: 'Active/Valid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0536',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0536 ( Certificate Status)
 */
export const V20536 = {
  Expired: v2_0536_Expired,
  Inactive: v2_0536_Inactive,
  Provisional: v2_0536_Provisional,
  Revoked: v2_0536_Revoked,
  ActiveValid: v2_0536_ActiveValid,
};
const v2_0538_Contractor: Coding = {
  code: 'CON',
  display: 'Contractor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0538',
};
const v2_0538_Consultant: Coding = {
  code: 'CST',
  display: 'Consultant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0538',
};
const v2_0538_Employee: Coding = {
  code: 'EMP',
  display: 'Employee',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0538',
};
const v2_0538_Volunteer: Coding = {
  code: 'VOL',
  display: 'Volunteer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0538',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0538 ( Institution Relationship Type)
 */
export const V20538 = {
  Contractor: v2_0538_Contractor,
  Consultant: v2_0538_Consultant,
  Employee: v2_0538_Employee,
  Volunteer: v2_0538_Volunteer,
};
const v2_0540_LeaveOfAbsence: Coding = {
  code: 'L',
  display: 'Leave of Absence',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0540',
};
const v2_0540_Retired: Coding = {
  code: 'R',
  display: 'Retired',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0540',
};
const v2_0540_Termination: Coding = {
  code: 'T',
  display: 'Termination',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0540',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0540 ( Inactive Reason Code)
 */
export const V20540 = {
  LeaveOfAbsence: v2_0540_LeaveOfAbsence,
  Retired: v2_0540_Retired,
  Termination: v2_0540_Termination,
};
const v2_0544_ContainerCracked: Coding = {
  code: 'CC',
  display: 'Container Cracked',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_ContainerLeaking: Coding = {
  code: 'CL',
  display: 'Container Leaking',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_ContainerTorn: Coding = {
  code: 'CT',
  display: 'Container Torn',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_SealBroken: Coding = {
  code: 'SB',
  display: 'Seal Broken',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_NotAmbientTemperature: Coding = {
  code: 'XAMB',
  display: 'Not Ambient temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_NotBodyTemperature: Coding = {
  code: 'XC37',
  display: 'Not Body temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_NotCriticalAmbientTemperature: Coding = {
  code: 'XCAMB',
  display: 'Not Critical ambient temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_ExposedToAir: Coding = {
  code: 'XCATM',
  display: 'Exposed to Air',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_NotCriticalFrozenTemperature: Coding = {
  code: 'XCFRZ',
  display: 'Not Critical frozen temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_NotCriticalRefrigeratedTemperature: Coding = {
  code: 'XCREF',
  display: 'Not Critical refrigerated temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_NotDeepFrozen: Coding = {
  code: 'XDFRZ',
  display: 'Not Deep frozen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_NotDry: Coding = {
  code: 'XDRY',
  display: 'Not Dry',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_NotFrozenTemperature: Coding = {
  code: 'XFRZ',
  display: 'Not Frozen temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_MetalExposed: Coding = {
  code: 'XMTLF',
  display: 'Metal Exposed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_NotLiquidNitrogen: Coding = {
  code: 'XNTR',
  display: 'Not Liquid nitrogen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_NotProtectedFromLight: Coding = {
  code: 'XPRTL',
  display: 'Not Protected from light',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_Shaken: Coding = {
  code: 'XPSA',
  display: 'Shaken',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_ExposedToShock: Coding = {
  code: 'XPSO',
  display: 'Exposed to shock',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_NotRefrigeratedTemperature: Coding = {
  code: 'XREF',
  display: 'Not Refrigerated temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_NotUltraFrozen: Coding = {
  code: 'XUFRZ',
  display: 'Not Ultra frozen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
const v2_0544_NotUpright: Coding = {
  code: 'XUPR',
  display: 'Not Upright',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0544',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0544 ( Container Condition)
 */
export const V20544 = {
  ContainerCracked: v2_0544_ContainerCracked,
  ContainerLeaking: v2_0544_ContainerLeaking,
  ContainerTorn: v2_0544_ContainerTorn,
  SealBroken: v2_0544_SealBroken,
  NotAmbientTemperature: v2_0544_NotAmbientTemperature,
  NotBodyTemperature: v2_0544_NotBodyTemperature,
  NotCriticalAmbientTemperature: v2_0544_NotCriticalAmbientTemperature,
  ExposedToAir: v2_0544_ExposedToAir,
  NotCriticalFrozenTemperature: v2_0544_NotCriticalFrozenTemperature,
  NotCriticalRefrigeratedTemperature: v2_0544_NotCriticalRefrigeratedTemperature,
  NotDeepFrozen: v2_0544_NotDeepFrozen,
  NotDry: v2_0544_NotDry,
  NotFrozenTemperature: v2_0544_NotFrozenTemperature,
  MetalExposed: v2_0544_MetalExposed,
  NotLiquidNitrogen: v2_0544_NotLiquidNitrogen,
  NotProtectedFromLight: v2_0544_NotProtectedFromLight,
  Shaken: v2_0544_Shaken,
  ExposedToShock: v2_0544_ExposedToShock,
  NotRefrigeratedTemperature: v2_0544_NotRefrigeratedTemperature,
  NotUltraFrozen: v2_0544_NotUltraFrozen,
  NotUpright: v2_0544_NotUpright,
};
const v2_0547_CountyParish: Coding = {
  code: 'C',
  display: 'County/Parish',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0547',
};
const v2_0547_Country: Coding = {
  code: 'N',
  display: 'Country',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0547',
};
const v2_0547_StateProvince: Coding = {
  code: 'S',
  display: 'State/Province',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0547',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0547 ( Jurisdictional Breadth)
 */
export const V20547 = {
  CountyParish: v2_0547_CountyParish,
  Country: v2_0547_Country,
  StateProvince: v2_0547_StateProvince,
};
const v2_0548_Self: Coding = {
  code: '1',
  display: 'Self',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0548',
};
const v2_0548_Parent: Coding = {
  code: '2',
  display: 'Parent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0548',
};
const v2_0548_NextOfKin: Coding = {
  code: '3',
  display: 'Next of Kin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0548',
};
const v2_0548_DurablePowerOfAttorneyInHealthcareAffairs: Coding = {
  code: '4',
  display: 'Durable Power of Attorney in Healthcare Affairs',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0548',
};
const v2_0548_Conservator: Coding = {
  code: '5',
  display: 'Conservator',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0548',
};
const v2_0548_EmergentPractitionerPractitionerJudgingCaseAsEmergencyRequiringCareWithoutAConsent: Coding = {
  code: '6',
  display: 'Emergent Practitioner (practitioner judging case as emergency requiring care without a consent)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0548',
};
const v2_0548_NonEmergentPractitionerIEMedicalEthicsCommittee: Coding = {
  code: '7',
  display: 'Non-Emergent Practitioner (i.e. medical ethics committee)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0548',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0548 ( Signatory's Relationship to Subject)
 */
export const V20548 = {
  Self: v2_0548_Self,
  Parent: v2_0548_Parent,
  NextOfKin: v2_0548_NextOfKin,
  DurablePowerOfAttorneyInHealthcareAffairs: v2_0548_DurablePowerOfAttorneyInHealthcareAffairs,
  Conservator: v2_0548_Conservator,
  EmergentPractitionerPractitionerJudgingCaseAsEmergencyRequiringCareWithoutAConsent: v2_0548_EmergentPractitionerPractitionerJudgingCaseAsEmergencyRequiringCareWithoutAConsent,
  NonEmergentPractitionerIEMedicalEthicsCommittee: v2_0548_NonEmergentPractitionerIEMedicalEthicsCommittee,
};
const v2_0550_ExternalJugular: Coding = {
  code: 'Â',
  display: 'External Jugular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Acetabulum: Coding = {
  code: 'ACET',
  display: 'Acetabulum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Achilles: Coding = {
  code: 'ACHIL',
  display: 'Achilles',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Abdomen: Coding = {
  code: 'ADB',
  display: 'Abdomen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Adenoids: Coding = {
  code: 'ADE',
  display: 'Adenoids',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Adrenal: Coding = {
  code: 'ADR',
  display: 'Adrenal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_AmnioticFluid: Coding = {
  code: 'AMN',
  display: 'Amniotic fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_AmnioticSac: Coding = {
  code: 'AMS',
  display: 'Amniotic Sac',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Anal: Coding = {
  code: 'ANAL',
  display: 'Anal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Ankle: Coding = {
  code: 'ANKL',
  display: 'Ankle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Antecubital: Coding = {
  code: 'ANTEC',
  display: 'Antecubital',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_AntecubitalFossa: Coding = {
  code: 'ANTECF',
  display: 'Antecubital Fossa',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Antrum: Coding = {
  code: 'ANTR',
  display: 'Antrum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Anus: Coding = {
  code: 'ANUS',
  display: 'Anus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Aorta: Coding = {
  code: 'AORTA',
  display: 'Aorta',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Appendix: Coding = {
  code: 'APDX',
  display: 'Appendix',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_AorticRim: Coding = {
  code: 'AR',
  display: 'Aortic Rim',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Areola: Coding = {
  code: 'AREO',
  display: 'Areola',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Arm: Coding = {
  code: 'ARM',
  display: 'Arm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Artery: Coding = {
  code: 'ARTE',
  display: 'Artery',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Ascites: Coding = {
  code: 'ASCIT',
  display: 'Ascites',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_AsciticFluid: Coding = {
  code: 'ASCT',
  display: 'Ascitic Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Atrium: Coding = {
  code: 'ATR',
  display: 'Atrium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Auricular: Coding = {
  code: 'AURI',
  display: 'Auricular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_AorticValve: Coding = {
  code: 'AV',
  display: 'Aortic Valve',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Axilla: Coding = {
  code: 'AXI',
  display: 'Axilla',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Back: Coding = {
  code: 'BACK',
  display: 'Back',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BartholinDuct: Coding = {
  code: 'BARTD',
  display: 'Bartholin Duct',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BartholinGland: Coding = {
  code: 'BARTG',
  display: 'Bartholin Gland',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BrainCystFluid: Coding = {
  code: 'BCYS',
  display: 'Brain Cyst Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BodyWhole: Coding = {
  code: 'BDY',
  display: 'Body, Whole',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BileDuct: Coding = {
  code: 'BID',
  display: 'Bile Duct',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BileFluid: Coding = {
  code: 'BIFL',
  display: 'Bile fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Bladder: Coding = {
  code: 'BLAD',
  display: 'Bladder',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BloodWhole: Coding = {
  code: 'BLD',
  display: 'Blood, Whole',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BloodArterial: Coding = {
  code: 'BLDA',
  display: 'Blood,  Arterial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BloodCapillary: Coding = {
  code: 'BLDC',
  display: 'Blood,  Capillary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BloodVenous: Coding = {
  code: 'BLDV',
  display: 'Blood,  Venous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Blood: Coding = {
  code: 'BLOOD',
  display: 'Blood',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BoneMarrow: Coding = {
  code: 'BMAR',
  display: 'Bone marrow',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Bone: Coding = {
  code: 'BON',
  display: 'Bone',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Bowel: Coding = {
  code: 'BOWEL',
  display: 'Bowel',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BowelLarge: Coding = {
  code: 'BOWLA',
  display: 'Bowel, Large',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BowelSmall: Coding = {
  code: 'BOWSM',
  display: 'Bowel, Small',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Basophils: Coding = {
  code: 'BPH',
  display: 'Basophils',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Brachial: Coding = {
  code: 'BRA',
  display: 'Brachial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Brain: Coding = {
  code: 'BRAIN',
  display: 'Brain',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Bronchial: Coding = {
  code: 'BRO',
  display: 'Bronchial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BronchioleBronchiolar: Coding = {
  code: 'BROCH',
  display: 'Bronchiole/Bronchiolar',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BronchusBronchial: Coding = {
  code: 'BRONC',
  display: 'Bronchus/Bronchial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Eyebrow: Coding = {
  code: 'BROW',
  display: 'Eyebrow',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Breast: Coding = {
  code: 'BRST',
  display: 'Breast',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BreastFluid: Coding = {
  code: 'BRSTFL',
  display: 'Breast fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BartholinGlandFluid: Coding = {
  code: 'BRTGF',
  display: 'Bartholin Gland Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Broviac: Coding = {
  code: 'BRV',
  display: 'Broviac',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Buccal: Coding = {
  code: 'BUCCA',
  display: 'Buccal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Bursa: Coding = {
  code: 'BURSA',
  display: 'Bursa',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BursaFluid: Coding = {
  code: 'BURSF',
  display: 'Bursa Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Buttocks: Coding = {
  code: 'BUTT',
  display: 'Buttocks',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Calf: Coding = {
  code: 'CALF',
  display: 'Calf',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Canal: Coding = {
  code: 'CANAL',
  display: 'Canal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Canaliculis: Coding = {
  code: 'CANLI',
  display: 'Canaliculis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Canthus: Coding = {
  code: 'CANTH',
  display: 'Canthus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Carotid: Coding = {
  code: 'CARO',
  display: 'Carotid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Carpal: Coding = {
  code: 'CARP',
  display: 'Carpal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Cavity: Coding = {
  code: 'CAVIT',
  display: 'Cavity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_BloodCord: Coding = {
  code: 'CBLD',
  display: 'Blood, Cord',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CardiacMuscle: Coding = {
  code: 'CDM',
  display: 'Cardiac Muscle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CommonDuct: Coding = {
  code: 'CDUCT',
  display: 'Common Duct',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CecumCecal: Coding = {
  code: 'CECUM',
  display: 'Cecum/Cecal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CervixUterus: Coding = {
  code: 'CERVUT',
  display: 'Cervix/Uterus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CavityChest: Coding = {
  code: 'CHE',
  display: 'Cavity, Chest',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Cheek: Coding = {
  code: 'CHEEK',
  display: 'Cheek',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Chest: Coding = {
  code: 'CHES',
  display: 'Chest',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_ChestTube: Coding = {
  code: 'CHESTÂ',
  display: 'Chest Tube',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Chin: Coding = {
  code: 'CHIN',
  display: 'Chin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CircumcisionSite: Coding = {
  code: 'CIRCU',
  display: 'Circumcision Site',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_ClavicleClavicular: Coding = {
  code: 'CLAVI',
  display: 'Clavicle/Clavicular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Clitoris: Coding = {
  code: 'CLIT',
  display: 'Clitoris',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Clitoral: Coding = {
  code: 'CLITO',
  display: 'Clitoral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Cannula: Coding = {
  code: 'CNL',
  display: 'Cannula',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Coccygeal: Coding = {
  code: 'COCCG',
  display: 'Coccygeal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Coccyx: Coding = {
  code: 'COCCY',
  display: 'Coccyx',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Colon: Coding = {
  code: 'COLON',
  display: 'Colon',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Colostomy: Coding = {
  code: 'COLOS',
  display: 'Colostomy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Conjunctiva: Coding = {
  code: 'CONJ',
  display: 'Conjunctiva',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Cord: Coding = {
  code: 'COR',
  display: 'Cord',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Coral: Coding = {
  code: 'CORAL',
  display: 'Coral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CordBlood: Coding = {
  code: 'CORD',
  display: 'Cord Blood',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Cornea: Coding = {
  code: 'CORN',
  display: 'Cornea',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_ColostomyStoma: Coding = {
  code: 'COS',
  display: 'Colostomy Stoma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CraniumEthmoid: Coding = {
  code: 'CRANE',
  display: 'Cranium, ethmoid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CraniumFrontal: Coding = {
  code: 'CRANF',
  display: 'Cranium, frontal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CraniumOccipital: Coding = {
  code: 'CRANO',
  display: 'Cranium, occipital',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CraniumParietal: Coding = {
  code: 'CRANP',
  display: 'Cranium, parietal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CraniumSphenoid: Coding = {
  code: 'CRANS',
  display: 'Cranium, sphenoid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CraniumTemporal: Coding = {
  code: 'CRANT',
  display: 'Cranium, temporal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CerebralSpinalFluid: Coding = {
  code: 'CSF',
  display: 'Cerebral Spinal Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Cubitus: Coding = {
  code: 'CUBIT',
  display: 'Cubitus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Cuff: Coding = {
  code: 'CUFF',
  display: 'Cuff',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_CulDeSac: Coding = {
  code: 'CULD',
  display: 'Cul De Sac',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Culdocentesis: Coding = {
  code: 'CULDO',
  display: 'Culdocentesis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Cervix: Coding = {
  code: 'CVX',
  display: 'Cervix',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Deltoid: Coding = {
  code: 'DELT',
  display: 'Deltoid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_DentalGingiva: Coding = {
  code: 'DEN',
  display: 'Dental Gingiva',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Dental: Coding = {
  code: 'DENTA',
  display: 'Dental',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_DialysisFluid: Coding = {
  code: 'DIAF',
  display: 'Dialysis Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Digit: Coding = {
  code: 'DIGIT',
  display: 'Digit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Disc: Coding = {
  code: 'DISC',
  display: 'Disc',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_DorsumDorsal: Coding = {
  code: 'DORS',
  display: 'Dorsum/Dorsal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Diaphragm: Coding = {
  code: 'DPH',
  display: 'Diaphragm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_DuodenalFluid: Coding = {
  code: 'DUFL',
  display: 'Duodenal Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_DuodenumDuodenal: Coding = {
  code: 'DUODE',
  display: 'Duodenum/Duodenal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Dura: Coding = {
  code: 'DUR',
  display: 'Dura',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Ear: Coding = {
  code: 'EAR',
  display: 'Ear',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_EarBoneIncus: Coding = {
  code: 'EARBI',
  display: 'Ear bone, incus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_EarBoneMalleus: Coding = {
  code: 'EARBM',
  display: 'Ear bone, malleus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_EarBoneStapes: Coding = {
  code: 'EARBS',
  display: 'Ear bone,stapes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_EarLobe: Coding = {
  code: 'EARLO',
  display: 'Ear Lobe',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Endocervical: Coding = {
  code: 'EC',
  display: 'Endocervical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Elbow: Coding = {
  code: 'ELBOW',
  display: 'Elbow',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_ElbowJoint: Coding = {
  code: 'ELBOWJ',
  display: 'Elbow Joint',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Endocardium: Coding = {
  code: 'ENDC',
  display: 'Endocardium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Endometrium: Coding = {
  code: 'ENDM',
  display: 'Endometrium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Endolpthamitis: Coding = {
  code: 'EOLPH',
  display: 'endolpthamitis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Eosinophils: Coding = {
  code: 'EOS',
  display: 'Eosinophils',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Epididymis: Coding = {
  code: 'EPD',
  display: 'Epididymis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Epicardial: Coding = {
  code: 'EPICA',
  display: 'Epicardial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Epicardium: Coding = {
  code: 'EPICM',
  display: 'Epicardium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Epidural: Coding = {
  code: 'EPIDU',
  display: 'Epidural',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Epiglottis: Coding = {
  code: 'EPIGL',
  display: 'Epiglottis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Esophagus: Coding = {
  code: 'ESO',
  display: 'Esophagus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Esophageal: Coding = {
  code: 'ESOPG',
  display: 'Esophageal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Endotracheal: Coding = {
  code: 'ET',
  display: 'Endotracheal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Ethmoid: Coding = {
  code: 'ETHMO',
  display: 'Ethmoid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Endourethral: Coding = {
  code: 'EUR',
  display: 'Endourethral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Eye: Coding = {
  code: 'EYE',
  display: 'Eye',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Eyelid: Coding = {
  code: 'EYELI',
  display: 'Eyelid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Face: Coding = {
  code: 'FACE',
  display: 'Face',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_FallopianTube: Coding = {
  code: 'FALLT',
  display: 'Fallopian Tube',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_FacialBoneInferiorNasalConcha: Coding = {
  code: 'FBINC',
  display: 'Facial bone, inferior nasal concha',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_FacialBoneLacrimal: Coding = {
  code: 'FBLAC',
  display: 'Facial bone, lacrimal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_FacialBoneMaxilla: Coding = {
  code: 'FBMAX',
  display: 'Facial bone, maxilla',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_FacialBoneNasal: Coding = {
  code: 'FBNAS',
  display: 'Facial bone, nasal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_FacialBonePalatine: Coding = {
  code: 'FBPAL',
  display: 'Facial bone, palatine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_FacialBoneVomer: Coding = {
  code: 'FBVOM',
  display: 'Facial bone, vomer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_FacialBoneZygomatic: Coding = {
  code: 'FBZYG',
  display: 'Facial bone, zygomatic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Femoral: Coding = {
  code: 'FEMOR',
  display: 'Femoral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Femur: Coding = {
  code: 'FEMUR',
  display: 'Femur',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Fetus: Coding = {
  code: 'FET',
  display: 'Fetus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Fibula: Coding = {
  code: 'FIBU',
  display: 'Fibula',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Finger: Coding = {
  code: 'FING',
  display: 'Finger',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_FingerNail: Coding = {
  code: 'FINGN',
  display: 'Finger Nail',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_FemoralHead: Coding = {
  code: 'FMH',
  display: 'Femoral Head',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Follicle: Coding = {
  code: 'FOL',
  display: 'Follicle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Foot: Coding = {
  code: 'FOOT',
  display: 'Foot',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Forearm: Coding = {
  code: 'FOREA',
  display: 'Forearm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Forehead: Coding = {
  code: 'FOREH',
  display: 'Forehead',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Foreskin: Coding = {
  code: 'FORES',
  display: 'Foreskin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Fourchette: Coding = {
  code: 'FOURC',
  display: 'Fourchette',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_GallBladder: Coding = {
  code: 'GB',
  display: 'Gall Bladder',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Genital: Coding = {
  code: 'GEN',
  display: 'Genital',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_GenitalCervix: Coding = {
  code: 'GENC',
  display: 'Genital Cervix',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_GenitalLochia: Coding = {
  code: 'GENL',
  display: 'Genital Lochia',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_GenitalLesion: Coding = {
  code: 'GL',
  display: 'Genital Lesion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Gland: Coding = {
  code: 'GLAND',
  display: 'Gland',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Glans: Coding = {
  code: 'GLANS',
  display: 'Glans',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Gluteus: Coding = {
  code: 'GLUT',
  display: 'Gluteus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Gluteal: Coding = {
  code: 'GLUTE',
  display: 'Gluteal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_GluteusMedius: Coding = {
  code: 'GLUTM',
  display: 'Gluteus Medius',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Groin: Coding = {
  code: 'GROIN',
  display: 'Groin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Gum: Coding = {
  code: 'GUM',
  display: 'Gum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_GenitalVulva: Coding = {
  code: 'GVU',
  display: 'Genital - Vulva',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Hallux: Coding = {
  code: 'HAL',
  display: 'Hallux',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Hand: Coding = {
  code: 'HAND',
  display: 'Hand',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Hair: Coding = {
  code: 'HAR',
  display: 'Hair',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Heart: Coding = {
  code: 'HART',
  display: 'Heart',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Head: Coding = {
  code: 'HEAD',
  display: 'Head',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Heel: Coding = {
  code: 'HEEL',
  display: 'Heel',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Hemorrhoid: Coding = {
  code: 'HEM',
  display: 'Hemorrhoid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Hip: Coding = {
  code: 'HIP',
  display: 'Hip',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_HipJoint: Coding = {
  code: 'HIPJ',
  display: 'Hip Joint',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Humerus: Coding = {
  code: 'HUMER',
  display: 'Humerus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_HeartValve: Coding = {
  code: 'HV',
  display: 'Heart Valve',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_HeartValveBicuspid: Coding = {
  code: 'HVB',
  display: 'Heart Valve, Bicuspid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_HeartValveTricuspid: Coding = {
  code: 'HVT',
  display: 'Heart Valve, Tricuspid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Hymen: Coding = {
  code: 'HYMEN',
  display: 'Hymen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Intracervical: Coding = {
  code: 'ICX',
  display: 'Intracervical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_IlealConduit: Coding = {
  code: 'ILC',
  display: 'Ileal Conduit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_IlicalConduit: Coding = {
  code: 'ILCON',
  display: 'Ilical Conduit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_IliacCrest: Coding = {
  code: 'ILCR',
  display: 'Iliac Crest',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_IlealLoop: Coding = {
  code: 'ILE',
  display: 'Ileal Loop',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Ileostomy: Coding = {
  code: 'ILEOS',
  display: 'Ileostomy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Ileum: Coding = {
  code: 'ILEUM',
  display: 'Ileum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Iliac: Coding = {
  code: 'ILIAC',
  display: 'Iliac',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Intranasal: Coding = {
  code: 'INASA',
  display: 'Intranasal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Inguinal: Coding = {
  code: 'INGUI',
  display: 'Inguinal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_IntestineLarge: Coding = {
  code: 'INSTL',
  display: 'Intestine, Large',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_IntestineSmall: Coding = {
  code: 'INSTS',
  display: 'Intestine, Small',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Intestine: Coding = {
  code: 'INT',
  display: 'Intestine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Introitus: Coding = {
  code: 'INTRO',
  display: 'Introitus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Intrauterine: Coding = {
  code: 'INTRU',
  display: 'Intrauterine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Ischium: Coding = {
  code: 'ISCHI',
  display: 'Ischium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_LoopIshial: Coding = {
  code: 'ISH',
  display: 'Loop, Ishial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Jaw: Coding = {
  code: 'JAW',
  display: 'Jaw',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_JugularExternal: Coding = {
  code: 'JUGE',
  display: 'Jugular, External',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_JugularInternal: Coding = {
  code: 'JUGI',
  display: 'Jugular, Internal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Kidney: Coding = {
  code: 'KIDNÂ',
  display: 'Kidney',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Knee: Coding = {
  code: 'KNEE',
  display: 'Knee',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_KneeFluid: Coding = {
  code: 'KNEEF',
  display: 'Knee Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_KneeJoint: Coding = {
  code: 'KNEEJ',
  display: 'Knee Joint',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Labia: Coding = {
  code: 'LABIA',
  display: 'Labia',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_LabiaMajora: Coding = {
  code: 'LABMA',
  display: 'Labia Majora',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_LabiaMinora: Coding = {
  code: 'LABMI',
  display: 'Labia Minora',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Lacrimal: Coding = {
  code: 'LACRI',
  display: 'Lacrimal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Lamella: Coding = {
  code: 'LAM',
  display: 'Lamella',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Larynx: Coding = {
  code: 'LARYN',
  display: 'Larynx',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Leg: Coding = {
  code: 'LEG',
  display: 'Leg',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Lens: Coding = {
  code: 'LENS',
  display: 'Lens',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Lingual: Coding = {
  code: 'LING',
  display: 'Lingual',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Lingula: Coding = {
  code: 'LINGU',
  display: 'Lingula',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Lip: Coding = {
  code: 'LIP',
  display: 'Lip',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Liver: Coding = {
  code: 'LIVER',
  display: 'Liver',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Lumen: Coding = {
  code: 'LMN',
  display: 'Lumen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_LymphNode: Coding = {
  code: 'LN',
  display: 'Lymph Node',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_LymphNodeGroin: Coding = {
  code: 'LNG',
  display: 'Lymph Node, Groin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Lobe: Coding = {
  code: 'LOBE',
  display: 'Lobe',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Lochia: Coding = {
  code: 'LOCH',
  display: 'Lochia',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Lumbar: Coding = {
  code: 'LUMBA',
  display: 'Lumbar',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Lung: Coding = {
  code: 'LUNG',
  display: 'Lung',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Lymphocytes: Coding = {
  code: 'LYM',
  display: 'Lymphocytes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Macrophages: Coding = {
  code: 'MAC',
  display: 'Macrophages',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Malleolus: Coding = {
  code: 'MALLE',
  display: 'Malleolus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_MandibleMandibular: Coding = {
  code: 'MANDI',
  display: 'Mandible/Mandibular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Marrow: Coding = {
  code: 'MAR',
  display: 'Marrow',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Mastoid: Coding = {
  code: 'MAST',
  display: 'Mastoid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_MaxillaMaxillary: Coding = {
  code: 'MAXIL',
  display: 'Maxilla/Maxillary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_MaxillarySinus: Coding = {
  code: 'MAXS',
  display: 'Maxillary Sinus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Meatus: Coding = {
  code: 'MEATU',
  display: 'Meatus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Meconium: Coding = {
  code: 'MEC',
  display: 'Meconium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Mediastinum: Coding = {
  code: 'MEDST',
  display: 'Mediastinum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Medullary: Coding = {
  code: 'MEDU',
  display: 'Medullary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Metacarpal: Coding = {
  code: 'METAC',
  display: 'Metacarpal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Metatarsal: Coding = {
  code: 'METAT',
  display: 'Metatarsal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_MilkBreast: Coding = {
  code: 'MILK',
  display: 'Milk, Breast',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_MitralValve: Coding = {
  code: 'MITRL',
  display: 'Mitral Valve',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Molar: Coding = {
  code: 'MOLAR',
  display: 'Molar',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_MonsUreteris: Coding = {
  code: 'MONSU',
  display: 'Mons Ureteris',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_MonsVenerisMonsPubis: Coding = {
  code: 'MONSV',
  display: 'Mons Veneris(Mons Pubis)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Membrane: Coding = {
  code: 'MOU',
  display: 'Membrane',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Mouth: Coding = {
  code: 'MOUTH',
  display: 'Mouth',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_MonsPubis: Coding = {
  code: 'MP',
  display: 'Mons Pubis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Meninges: Coding = {
  code: 'MPB',
  display: 'Meninges',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Mrsa: Coding = {
  code: 'MRSA2',
  display: 'Mrsa:',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Myocardium: Coding = {
  code: 'MYO',
  display: 'Myocardium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Nail: Coding = {
  code: 'NAIL',
  display: 'Nail',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_NailBed: Coding = {
  code: 'NAILB',
  display: 'Nail Bed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_NailFinger: Coding = {
  code: 'NAILF',
  display: 'Nail, Finger',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_NailToe: Coding = {
  code: 'NAILT',
  display: 'Nail, Toe',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Nares: Coding = {
  code: 'NARES',
  display: 'Nares',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Nasal: Coding = {
  code: 'NASL',
  display: 'Nasal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Navel: Coding = {
  code: 'NAVEL',
  display: 'Navel',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Neck: Coding = {
  code: 'NECK',
  display: 'Neck',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Nerve: Coding = {
  code: 'NERVE',
  display: 'Nerve',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Nipple: Coding = {
  code: 'NIPPL',
  display: 'Nipple',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Nasolacrimal: Coding = {
  code: 'NLACR',
  display: 'Nasolacrimal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_NoseNasalPassage: Coding = {
  code: 'NOS',
  display: 'Nose (Nasal Passage)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_NoseNoseOutside: Coding = {
  code: 'NOSE',
  display: 'Nose/Nose(outside)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Nostril: Coding = {
  code: 'NOSTR',
  display: 'Nostril',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_NasopharyngealNasopharynx: Coding = {
  code: 'NP',
  display: 'Nasopharyngeal/Nasopharynx',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_NasalSeptum: Coding = {
  code: 'NSS',
  display: 'Nasal Septum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Nasotracheal: Coding = {
  code: 'NTRAC',
  display: 'Nasotracheal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Occipital: Coding = {
  code: 'OCCIP',
  display: 'Occipital',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Olecranon: Coding = {
  code: 'OLECR',
  display: 'Olecranon',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Omentum: Coding = {
  code: 'OMEN',
  display: 'Omentum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_OrbitOrbital: Coding = {
  code: 'ORBIT',
  display: 'Orbit/Orbital',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Oropharynx: Coding = {
  code: 'ORO',
  display: 'Oropharynx',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_OsCoxaPelvicGirdle: Coding = {
  code: 'OSCOX',
  display: 'Os coxa (pelvic girdle)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Ovary: Coding = {
  code: 'OVARY',
  display: 'Ovary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_PancreaticFluid: Coding = {
  code: 'PAFL',
  display: 'Pancreatic Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Palate: Coding = {
  code: 'PALAT',
  display: 'Palate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Palm: Coding = {
  code: 'PALM',
  display: 'Palm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_PerianalPerirectal: Coding = {
  code: 'PANAL',
  display: 'Perianal/Perirectal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Pancreas: Coding = {
  code: 'PANCR',
  display: 'Pancreas',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Paratracheal: Coding = {
  code: 'PARAT',
  display: 'Paratracheal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Parietal: Coding = {
  code: 'PARIE',
  display: 'Parietal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Paronychia: Coding = {
  code: 'PARON',
  display: 'Paronychia',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_ParotidParotidGland: Coding = {
  code: 'PAROT',
  display: 'Parotid/Parotid Gland',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Parasternal: Coding = {
  code: 'PAS',
  display: 'Parasternal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Patella: Coding = {
  code: 'PATEL',
  display: 'Patella',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Pericardium: Coding = {
  code: 'PCARD',
  display: 'Pericardium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Periclitoral: Coding = {
  code: 'PCLIT',
  display: 'Periclitoral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Pelvis: Coding = {
  code: 'PELV',
  display: 'Pelvis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Penis: Coding = {
  code: 'PENIS',
  display: 'Penis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_PenileShaft: Coding = {
  code: 'PENSH',
  display: 'Penile Shaft',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Peritoneal: Coding = {
  code: 'PER',
  display: 'Peritoneal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_PericardialFluid: Coding = {
  code: 'PERI',
  display: 'Pericardial Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Perihepatic: Coding = {
  code: 'PERIH',
  display: 'Perihepatic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_PerinealAbscess: Coding = {
  code: 'PERIN',
  display: 'Perineal Abscess',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Perisplenic: Coding = {
  code: 'PERIS',
  display: 'Perisplenic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Peritoneum: Coding = {
  code: 'PERIT',
  display: 'Peritoneum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Periurethal: Coding = {
  code: 'PERIU',
  display: 'Periurethal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Perivesicular: Coding = {
  code: 'PERIV',
  display: 'Perivesicular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Perirectal: Coding = {
  code: 'PERRA',
  display: 'Perirectal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_PeritonealFluid: Coding = {
  code: 'PERT',
  display: 'Peritoneal Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Phalanyx: Coding = {
  code: 'PHALA',
  display: 'Phalanyx',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Pilonidal: Coding = {
  code: 'PILO',
  display: 'Pilonidal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Pinna: Coding = {
  code: 'PINNA',
  display: 'Pinna',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_PlacentaFetalSide: Coding = {
  code: 'PLACF',
  display: 'Placenta (Fetal Side)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_PlacentaMaternalSide: Coding = {
  code: 'PLACM',
  display: 'Placenta (Maternal Side)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Plantar: Coding = {
  code: 'PLANT',
  display: 'Plantar',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_PalateHard: Coding = {
  code: 'PLATH',
  display: 'Palate, Hard',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_PalateSoft: Coding = {
  code: 'PLATS',
  display: 'Palate, Soft',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Placenta: Coding = {
  code: 'PLC',
  display: 'Placenta',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_PleuralFluid: Coding = {
  code: 'PLEU',
  display: 'Pleural Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Pleura: Coding = {
  code: 'PLEUR',
  display: 'Pleura',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_PleuralFluidThoracentesisFld: Coding = {
  code: 'PLR',
  display: 'Pleural Fluid (Thoracentesis Fld)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Perineal: Coding = {
  code: 'PNEAL',
  display: 'Perineal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Perinephric: Coding = {
  code: 'PNEPH',
  display: 'Perinephric',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Perineum: Coding = {
  code: 'PNM',
  display: 'Perineum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Popliteal: Coding = {
  code: 'POPLI',
  display: 'Popliteal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Periorbital: Coding = {
  code: 'PORBI',
  display: 'Periorbital',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Preauricular: Coding = {
  code: 'PREAU',
  display: 'Preauricular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Prerenal: Coding = {
  code: 'PRERE',
  display: 'Prerenal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_ProstaticFluid: Coding = {
  code: 'PROS',
  display: 'Prostatic Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_ProstateGland: Coding = {
  code: 'PRST',
  display: 'Prostate Gland',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Peritonsillar: Coding = {
  code: 'PTONS',
  display: 'Peritonsillar',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Pubic: Coding = {
  code: 'PUBIC',
  display: 'Pubic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_PulmonaryArtery: Coding = {
  code: 'PUL',
  display: 'Pulmonary Artery',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Radial: Coding = {
  code: 'RADI',
  display: 'Radial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Radius: Coding = {
  code: 'RADIUS',
  display: 'Radius',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_RedBloodCells: Coding = {
  code: 'RBC',
  display: 'Red Blood Cells',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Rectal: Coding = {
  code: 'RECTL',
  display: 'Rectal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Rectum: Coding = {
  code: 'RECTU',
  display: 'Rectum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Renal: Coding = {
  code: 'RENL',
  display: 'Renal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Rib: Coding = {
  code: 'RIB',
  display: 'Rib',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_RenalPelvis: Coding = {
  code: 'RNP',
  display: 'Renal Pelvis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Retroperitoneal: Coding = {
  code: 'RPERI',
  display: 'Retroperitoneal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_UterineCulDeSac: Coding = {
  code: 'SAC',
  display: 'Uterine Cul/De/Sac',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Sacroiliac: Coding = {
  code: 'SACIL',
  display: 'Sacroiliac',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Sacral: Coding = {
  code: 'SACRA',
  display: 'Sacral',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Sacrococcygeal: Coding = {
  code: 'SACRO',
  display: 'Sacrococcygeal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Sacrum: Coding = {
  code: 'SACRU',
  display: 'Sacrum',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SalivaryGland: Coding = {
  code: 'SALGL',
  display: 'Salivary Gland',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Scalp: Coding = {
  code: 'SCALP',
  display: 'Scalp',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_ScapulaScapular: Coding = {
  code: 'SCAPU',
  display: 'Scapula/Scapular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SupraclavicleSupraclavicular: Coding = {
  code: 'SCLAV',
  display: 'Supraclavicle/Supraclavicular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Sclera: Coding = {
  code: 'SCLER',
  display: 'Sclera',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SubClavian: Coding = {
  code: 'SCLV',
  display: 'Sub Clavian',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_ScrotumScrotal: Coding = {
  code: 'SCROT',
  display: 'Scrotum/Scrotal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Subdiaphramatic: Coding = {
  code: 'SDP',
  display: 'Subdiaphramatic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SeminalFluid: Coding = {
  code: 'SEM',
  display: 'Seminal Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Semen: Coding = {
  code: 'SEMN',
  display: 'Semen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SeptumSeptal: Coding = {
  code: 'SEPTU',
  display: 'Septum/Septal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Seroma: Coding = {
  code: 'SEROM',
  display: 'Seroma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SubgalealFluid: Coding = {
  code: 'SGF',
  display: 'Subgaleal Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Shin: Coding = {
  code: 'SHIN',
  display: 'Shin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Shoulder: Coding = {
  code: 'SHOL',
  display: 'Shoulder',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SholderJoint: Coding = {
  code: 'SHOLJ',
  display: 'Sholder Joint',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Sigmoid: Coding = {
  code: 'SIGMO',
  display: 'Sigmoid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Sinus: Coding = {
  code: 'SINUS',
  display: 'Sinus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SkeneQuoteSGland: Coding = {
  code: 'SKENE',
  display: "Skene's Gland",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SkeletalMuscle: Coding = {
  code: 'SKM',
  display: 'Skeletal Muscle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Skull: Coding = {
  code: 'SKULL',
  display: 'Skull',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Sole: Coding = {
  code: 'SOLE',
  display: 'Sole',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SpinalCord: Coding = {
  code: 'SPCOR',
  display: 'Spinal Cord',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Sphenoid: Coding = {
  code: 'SPHEN',
  display: 'Sphenoid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Spleen: Coding = {
  code: 'SPLN',
  display: 'Spleen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Spermatozoa: Coding = {
  code: 'SPRM',
  display: 'Spermatozoa',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SupraCervical: Coding = {
  code: 'SPX',
  display: 'Supra Cervical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SternumSternal: Coding = {
  code: 'STER',
  display: 'Sternum/Sternal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Stoma: Coding = {
  code: 'STOM',
  display: 'Stoma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Stomach: Coding = {
  code: 'STOMA',
  display: 'Stomach',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_LiquidStool: Coding = {
  code: 'STOOLL',
  display: 'Liquid Stool',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Stump: Coding = {
  code: 'STUMP',
  display: 'Stump',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Subdural: Coding = {
  code: 'SUB',
  display: 'Subdural',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SubduralFluid: Coding = {
  code: 'SUBD',
  display: 'Subdural Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Submandibular: Coding = {
  code: 'SUBM',
  display: 'Submandibular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Submental: Coding = {
  code: 'SUBME',
  display: 'Submental',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Subphrenic: Coding = {
  code: 'SUBPH',
  display: 'Subphrenic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Submaxillary: Coding = {
  code: 'SUBX',
  display: 'Submaxillary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SuprapubicSpecimen: Coding = {
  code: 'SUPB',
  display: 'Suprapubic Specimen',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Suprapubic: Coding = {
  code: 'SUPRA',
  display: 'Suprapubic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Sweat: Coding = {
  code: 'SWT',
  display: 'Sweat',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SweatGland: Coding = {
  code: 'SWTG',
  display: 'Sweat Gland',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_SynovialFluid: Coding = {
  code: 'SYN',
  display: 'Synovial Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Synovial: Coding = {
  code: 'SYNOL',
  display: 'Synovial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Synovium: Coding = {
  code: 'SYNOV',
  display: 'Synovium',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Tarsal: Coding = {
  code: 'TARS',
  display: 'Tarsal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Transbronchial: Coding = {
  code: 'TBRON',
  display: 'Transbronchial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_TranscarinaAsp: Coding = {
  code: 'TCN',
  display: 'Transcarina Asp',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_TearDuct: Coding = {
  code: 'TDUCT',
  display: 'Tear Duct',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Tears: Coding = {
  code: 'TEAR',
  display: 'Tears',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Temple: Coding = {
  code: 'TEMPL',
  display: 'Temple',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Temporal: Coding = {
  code: 'TEMPO',
  display: 'Temporal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_TesticleTestis: Coding = {
  code: 'TESTI',
  display: 'Testicle(Testis)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Thigh: Coding = {
  code: 'THIGH',
  display: 'Thigh',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Thymus: Coding = {
  code: 'THM',
  display: 'Thymus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_ThoraxThoracicThoracentesis: Coding = {
  code: 'THORA',
  display: 'Thorax/Thoracic/Thoracentesis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Throat: Coding = {
  code: 'THRB',
  display: 'Throat',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Thumb: Coding = {
  code: 'THUMB',
  display: 'Thumb',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Thyroid: Coding = {
  code: 'THYRD',
  display: 'Thyroid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Tibia: Coding = {
  code: 'TIBIA',
  display: 'Tibia',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_TemporalLobe: Coding = {
  code: 'TML',
  display: 'Temporal Lobe',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Thumbnail: Coding = {
  code: 'TNL',
  display: 'Thumbnail',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Toe: Coding = {
  code: 'TOE',
  display: 'Toe',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_ToeNail: Coding = {
  code: 'TOEN',
  display: 'Toe Nail',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Tongue: Coding = {
  code: 'TONG',
  display: 'Tongue',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Tonsil: Coding = {
  code: 'TONS',
  display: 'Tonsil',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Tooth: Coding = {
  code: 'TOOTH',
  display: 'Tooth',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_TracheaTracheal: Coding = {
  code: 'TRCHE',
  display: 'Trachea/Tracheal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_ToothSocket: Coding = {
  code: 'TSK',
  display: 'Tooth Socket',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_UlnaUlnar: Coding = {
  code: 'ULNA',
  display: 'Ulna/Ulnar',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_UmbilicalBlood: Coding = {
  code: 'UMB',
  display: 'Umbilical Blood',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_UmbilicusUmbilical: Coding = {
  code: 'UMBL',
  display: 'Umbilicus/Umbilical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Ureter: Coding = {
  code: 'URET',
  display: 'Ureter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Urethra: Coding = {
  code: 'URTH',
  display: 'Urethra',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_StomaUrinary: Coding = {
  code: 'USTOM',
  display: 'Stoma, Urinary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Uterus: Coding = {
  code: 'UTER',
  display: 'Uterus',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Uterine: Coding = {
  code: 'UTERI',
  display: 'Uterine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VaginaVaginal: Coding = {
  code: 'VAGIN',
  display: 'Vagina/Vaginal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Valve: Coding = {
  code: 'VAL',
  display: 'Valve',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VasDeferens: Coding = {
  code: 'VAS',
  display: 'Vas Deferens',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VastusLateralis: Coding = {
  code: 'VASTL',
  display: 'Vastus Lateralis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Vault: Coding = {
  code: 'VAULT',
  display: 'Vault',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VentricularCSF: Coding = {
  code: 'VCSF',
  display: 'Ventricular CSF',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VaginalCuff: Coding = {
  code: 'VCUFF',
  display: 'Vaginal Cuff',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Vein: Coding = {
  code: 'VEIN',
  display: 'Vein',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Ventragluteal: Coding = {
  code: 'VENTG',
  display: 'Ventragluteal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VermisCerebelli: Coding = {
  code: 'VERMI',
  display: 'Vermis Cerebelli',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VertebraCervical: Coding = {
  code: 'VERTC',
  display: 'Vertebra, cervical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VertebraLumbar: Coding = {
  code: 'VERTL',
  display: 'Vertebra, lumbar',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VertebraThoracic: Coding = {
  code: 'VERTT',
  display: 'Vertebra, thoracic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Vesicular: Coding = {
  code: 'VESCL',
  display: 'Vesicular',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VesicularFluid: Coding = {
  code: 'VESFLD',
  display: 'Vesicular Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Vesicle: Coding = {
  code: 'VESI',
  display: 'Vesicle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VestibuleGenital: Coding = {
  code: 'VESTI',
  display: 'Vestibule(Genital)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VaginalVault: Coding = {
  code: 'VGV',
  display: 'Vaginal Vault',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VitreousFluid: Coding = {
  code: 'VITR',
  display: 'Vitreous Fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_VocalCord: Coding = {
  code: 'VOC',
  display: 'Vocal Cord',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Vulva: Coding = {
  code: 'VULVA',
  display: 'Vulva',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Leukocytes: Coding = {
  code: 'WBC',
  display: 'Leukocytes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
const v2_0550_Wrist: Coding = {
  code: 'WRIST',
  display: 'Wrist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0550',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0550 ( Body Parts)
 */
export const V20550 = {
  ExternalJugular: v2_0550_ExternalJugular,
  Acetabulum: v2_0550_Acetabulum,
  Achilles: v2_0550_Achilles,
  Abdomen: v2_0550_Abdomen,
  Adenoids: v2_0550_Adenoids,
  Adrenal: v2_0550_Adrenal,
  AmnioticFluid: v2_0550_AmnioticFluid,
  AmnioticSac: v2_0550_AmnioticSac,
  Anal: v2_0550_Anal,
  Ankle: v2_0550_Ankle,
  Antecubital: v2_0550_Antecubital,
  AntecubitalFossa: v2_0550_AntecubitalFossa,
  Antrum: v2_0550_Antrum,
  Anus: v2_0550_Anus,
  Aorta: v2_0550_Aorta,
  Appendix: v2_0550_Appendix,
  AorticRim: v2_0550_AorticRim,
  Areola: v2_0550_Areola,
  Arm: v2_0550_Arm,
  Artery: v2_0550_Artery,
  Ascites: v2_0550_Ascites,
  AsciticFluid: v2_0550_AsciticFluid,
  Atrium: v2_0550_Atrium,
  Auricular: v2_0550_Auricular,
  AorticValve: v2_0550_AorticValve,
  Axilla: v2_0550_Axilla,
  Back: v2_0550_Back,
  BartholinDuct: v2_0550_BartholinDuct,
  BartholinGland: v2_0550_BartholinGland,
  BrainCystFluid: v2_0550_BrainCystFluid,
  BodyWhole: v2_0550_BodyWhole,
  BileDuct: v2_0550_BileDuct,
  BileFluid: v2_0550_BileFluid,
  Bladder: v2_0550_Bladder,
  BloodWhole: v2_0550_BloodWhole,
  BloodArterial: v2_0550_BloodArterial,
  BloodCapillary: v2_0550_BloodCapillary,
  BloodVenous: v2_0550_BloodVenous,
  Blood: v2_0550_Blood,
  BoneMarrow: v2_0550_BoneMarrow,
  Bone: v2_0550_Bone,
  Bowel: v2_0550_Bowel,
  BowelLarge: v2_0550_BowelLarge,
  BowelSmall: v2_0550_BowelSmall,
  Basophils: v2_0550_Basophils,
  Brachial: v2_0550_Brachial,
  Brain: v2_0550_Brain,
  Bronchial: v2_0550_Bronchial,
  BronchioleBronchiolar: v2_0550_BronchioleBronchiolar,
  BronchusBronchial: v2_0550_BronchusBronchial,
  Eyebrow: v2_0550_Eyebrow,
  Breast: v2_0550_Breast,
  BreastFluid: v2_0550_BreastFluid,
  BartholinGlandFluid: v2_0550_BartholinGlandFluid,
  Broviac: v2_0550_Broviac,
  Buccal: v2_0550_Buccal,
  Bursa: v2_0550_Bursa,
  BursaFluid: v2_0550_BursaFluid,
  Buttocks: v2_0550_Buttocks,
  Calf: v2_0550_Calf,
  Canal: v2_0550_Canal,
  Canaliculis: v2_0550_Canaliculis,
  Canthus: v2_0550_Canthus,
  Carotid: v2_0550_Carotid,
  Carpal: v2_0550_Carpal,
  Cavity: v2_0550_Cavity,
  BloodCord: v2_0550_BloodCord,
  CardiacMuscle: v2_0550_CardiacMuscle,
  CommonDuct: v2_0550_CommonDuct,
  CecumCecal: v2_0550_CecumCecal,
  CervixUterus: v2_0550_CervixUterus,
  CavityChest: v2_0550_CavityChest,
  Cheek: v2_0550_Cheek,
  Chest: v2_0550_Chest,
  ChestTube: v2_0550_ChestTube,
  Chin: v2_0550_Chin,
  CircumcisionSite: v2_0550_CircumcisionSite,
  ClavicleClavicular: v2_0550_ClavicleClavicular,
  Clitoris: v2_0550_Clitoris,
  Clitoral: v2_0550_Clitoral,
  Cannula: v2_0550_Cannula,
  Coccygeal: v2_0550_Coccygeal,
  Coccyx: v2_0550_Coccyx,
  Colon: v2_0550_Colon,
  Colostomy: v2_0550_Colostomy,
  Conjunctiva: v2_0550_Conjunctiva,
  Cord: v2_0550_Cord,
  Coral: v2_0550_Coral,
  CordBlood: v2_0550_CordBlood,
  Cornea: v2_0550_Cornea,
  ColostomyStoma: v2_0550_ColostomyStoma,
  CraniumEthmoid: v2_0550_CraniumEthmoid,
  CraniumFrontal: v2_0550_CraniumFrontal,
  CraniumOccipital: v2_0550_CraniumOccipital,
  CraniumParietal: v2_0550_CraniumParietal,
  CraniumSphenoid: v2_0550_CraniumSphenoid,
  CraniumTemporal: v2_0550_CraniumTemporal,
  CerebralSpinalFluid: v2_0550_CerebralSpinalFluid,
  Cubitus: v2_0550_Cubitus,
  Cuff: v2_0550_Cuff,
  CulDeSac: v2_0550_CulDeSac,
  Culdocentesis: v2_0550_Culdocentesis,
  Cervix: v2_0550_Cervix,
  Deltoid: v2_0550_Deltoid,
  DentalGingiva: v2_0550_DentalGingiva,
  Dental: v2_0550_Dental,
  DialysisFluid: v2_0550_DialysisFluid,
  Digit: v2_0550_Digit,
  Disc: v2_0550_Disc,
  DorsumDorsal: v2_0550_DorsumDorsal,
  Diaphragm: v2_0550_Diaphragm,
  DuodenalFluid: v2_0550_DuodenalFluid,
  DuodenumDuodenal: v2_0550_DuodenumDuodenal,
  Dura: v2_0550_Dura,
  Ear: v2_0550_Ear,
  EarBoneIncus: v2_0550_EarBoneIncus,
  EarBoneMalleus: v2_0550_EarBoneMalleus,
  EarBoneStapes: v2_0550_EarBoneStapes,
  EarLobe: v2_0550_EarLobe,
  Endocervical: v2_0550_Endocervical,
  Elbow: v2_0550_Elbow,
  ElbowJoint: v2_0550_ElbowJoint,
  Endocardium: v2_0550_Endocardium,
  Endometrium: v2_0550_Endometrium,
  Endolpthamitis: v2_0550_Endolpthamitis,
  Eosinophils: v2_0550_Eosinophils,
  Epididymis: v2_0550_Epididymis,
  Epicardial: v2_0550_Epicardial,
  Epicardium: v2_0550_Epicardium,
  Epidural: v2_0550_Epidural,
  Epiglottis: v2_0550_Epiglottis,
  Esophagus: v2_0550_Esophagus,
  Esophageal: v2_0550_Esophageal,
  Endotracheal: v2_0550_Endotracheal,
  Ethmoid: v2_0550_Ethmoid,
  Endourethral: v2_0550_Endourethral,
  Eye: v2_0550_Eye,
  Eyelid: v2_0550_Eyelid,
  Face: v2_0550_Face,
  FallopianTube: v2_0550_FallopianTube,
  FacialBoneInferiorNasalConcha: v2_0550_FacialBoneInferiorNasalConcha,
  FacialBoneLacrimal: v2_0550_FacialBoneLacrimal,
  FacialBoneMaxilla: v2_0550_FacialBoneMaxilla,
  FacialBoneNasal: v2_0550_FacialBoneNasal,
  FacialBonePalatine: v2_0550_FacialBonePalatine,
  FacialBoneVomer: v2_0550_FacialBoneVomer,
  FacialBoneZygomatic: v2_0550_FacialBoneZygomatic,
  Femoral: v2_0550_Femoral,
  Femur: v2_0550_Femur,
  Fetus: v2_0550_Fetus,
  Fibula: v2_0550_Fibula,
  Finger: v2_0550_Finger,
  FingerNail: v2_0550_FingerNail,
  FemoralHead: v2_0550_FemoralHead,
  Follicle: v2_0550_Follicle,
  Foot: v2_0550_Foot,
  Forearm: v2_0550_Forearm,
  Forehead: v2_0550_Forehead,
  Foreskin: v2_0550_Foreskin,
  Fourchette: v2_0550_Fourchette,
  GallBladder: v2_0550_GallBladder,
  Genital: v2_0550_Genital,
  GenitalCervix: v2_0550_GenitalCervix,
  GenitalLochia: v2_0550_GenitalLochia,
  GenitalLesion: v2_0550_GenitalLesion,
  Gland: v2_0550_Gland,
  Glans: v2_0550_Glans,
  Gluteus: v2_0550_Gluteus,
  Gluteal: v2_0550_Gluteal,
  GluteusMedius: v2_0550_GluteusMedius,
  Groin: v2_0550_Groin,
  Gum: v2_0550_Gum,
  GenitalVulva: v2_0550_GenitalVulva,
  Hallux: v2_0550_Hallux,
  Hand: v2_0550_Hand,
  Hair: v2_0550_Hair,
  Heart: v2_0550_Heart,
  Head: v2_0550_Head,
  Heel: v2_0550_Heel,
  Hemorrhoid: v2_0550_Hemorrhoid,
  Hip: v2_0550_Hip,
  HipJoint: v2_0550_HipJoint,
  Humerus: v2_0550_Humerus,
  HeartValve: v2_0550_HeartValve,
  HeartValveBicuspid: v2_0550_HeartValveBicuspid,
  HeartValveTricuspid: v2_0550_HeartValveTricuspid,
  Hymen: v2_0550_Hymen,
  Intracervical: v2_0550_Intracervical,
  IlealConduit: v2_0550_IlealConduit,
  IlicalConduit: v2_0550_IlicalConduit,
  IliacCrest: v2_0550_IliacCrest,
  IlealLoop: v2_0550_IlealLoop,
  Ileostomy: v2_0550_Ileostomy,
  Ileum: v2_0550_Ileum,
  Iliac: v2_0550_Iliac,
  Intranasal: v2_0550_Intranasal,
  Inguinal: v2_0550_Inguinal,
  IntestineLarge: v2_0550_IntestineLarge,
  IntestineSmall: v2_0550_IntestineSmall,
  Intestine: v2_0550_Intestine,
  Introitus: v2_0550_Introitus,
  Intrauterine: v2_0550_Intrauterine,
  Ischium: v2_0550_Ischium,
  LoopIshial: v2_0550_LoopIshial,
  Jaw: v2_0550_Jaw,
  JugularExternal: v2_0550_JugularExternal,
  JugularInternal: v2_0550_JugularInternal,
  Kidney: v2_0550_Kidney,
  Knee: v2_0550_Knee,
  KneeFluid: v2_0550_KneeFluid,
  KneeJoint: v2_0550_KneeJoint,
  Labia: v2_0550_Labia,
  LabiaMajora: v2_0550_LabiaMajora,
  LabiaMinora: v2_0550_LabiaMinora,
  Lacrimal: v2_0550_Lacrimal,
  Lamella: v2_0550_Lamella,
  Larynx: v2_0550_Larynx,
  Leg: v2_0550_Leg,
  Lens: v2_0550_Lens,
  Lingual: v2_0550_Lingual,
  Lingula: v2_0550_Lingula,
  Lip: v2_0550_Lip,
  Liver: v2_0550_Liver,
  Lumen: v2_0550_Lumen,
  LymphNode: v2_0550_LymphNode,
  LymphNodeGroin: v2_0550_LymphNodeGroin,
  Lobe: v2_0550_Lobe,
  Lochia: v2_0550_Lochia,
  Lumbar: v2_0550_Lumbar,
  Lung: v2_0550_Lung,
  Lymphocytes: v2_0550_Lymphocytes,
  Macrophages: v2_0550_Macrophages,
  Malleolus: v2_0550_Malleolus,
  MandibleMandibular: v2_0550_MandibleMandibular,
  Marrow: v2_0550_Marrow,
  Mastoid: v2_0550_Mastoid,
  MaxillaMaxillary: v2_0550_MaxillaMaxillary,
  MaxillarySinus: v2_0550_MaxillarySinus,
  Meatus: v2_0550_Meatus,
  Meconium: v2_0550_Meconium,
  Mediastinum: v2_0550_Mediastinum,
  Medullary: v2_0550_Medullary,
  Metacarpal: v2_0550_Metacarpal,
  Metatarsal: v2_0550_Metatarsal,
  MilkBreast: v2_0550_MilkBreast,
  MitralValve: v2_0550_MitralValve,
  Molar: v2_0550_Molar,
  MonsUreteris: v2_0550_MonsUreteris,
  MonsVenerisMonsPubis: v2_0550_MonsVenerisMonsPubis,
  Membrane: v2_0550_Membrane,
  Mouth: v2_0550_Mouth,
  MonsPubis: v2_0550_MonsPubis,
  Meninges: v2_0550_Meninges,
  Mrsa: v2_0550_Mrsa,
  Myocardium: v2_0550_Myocardium,
  Nail: v2_0550_Nail,
  NailBed: v2_0550_NailBed,
  NailFinger: v2_0550_NailFinger,
  NailToe: v2_0550_NailToe,
  Nares: v2_0550_Nares,
  Nasal: v2_0550_Nasal,
  Navel: v2_0550_Navel,
  Neck: v2_0550_Neck,
  Nerve: v2_0550_Nerve,
  Nipple: v2_0550_Nipple,
  Nasolacrimal: v2_0550_Nasolacrimal,
  NoseNasalPassage: v2_0550_NoseNasalPassage,
  NoseNoseOutside: v2_0550_NoseNoseOutside,
  Nostril: v2_0550_Nostril,
  NasopharyngealNasopharynx: v2_0550_NasopharyngealNasopharynx,
  NasalSeptum: v2_0550_NasalSeptum,
  Nasotracheal: v2_0550_Nasotracheal,
  Occipital: v2_0550_Occipital,
  Olecranon: v2_0550_Olecranon,
  Omentum: v2_0550_Omentum,
  OrbitOrbital: v2_0550_OrbitOrbital,
  Oropharynx: v2_0550_Oropharynx,
  OsCoxaPelvicGirdle: v2_0550_OsCoxaPelvicGirdle,
  Ovary: v2_0550_Ovary,
  PancreaticFluid: v2_0550_PancreaticFluid,
  Palate: v2_0550_Palate,
  Palm: v2_0550_Palm,
  PerianalPerirectal: v2_0550_PerianalPerirectal,
  Pancreas: v2_0550_Pancreas,
  Paratracheal: v2_0550_Paratracheal,
  Parietal: v2_0550_Parietal,
  Paronychia: v2_0550_Paronychia,
  ParotidParotidGland: v2_0550_ParotidParotidGland,
  Parasternal: v2_0550_Parasternal,
  Patella: v2_0550_Patella,
  Pericardium: v2_0550_Pericardium,
  Periclitoral: v2_0550_Periclitoral,
  Pelvis: v2_0550_Pelvis,
  Penis: v2_0550_Penis,
  PenileShaft: v2_0550_PenileShaft,
  Peritoneal: v2_0550_Peritoneal,
  PericardialFluid: v2_0550_PericardialFluid,
  Perihepatic: v2_0550_Perihepatic,
  PerinealAbscess: v2_0550_PerinealAbscess,
  Perisplenic: v2_0550_Perisplenic,
  Peritoneum: v2_0550_Peritoneum,
  Periurethal: v2_0550_Periurethal,
  Perivesicular: v2_0550_Perivesicular,
  Perirectal: v2_0550_Perirectal,
  PeritonealFluid: v2_0550_PeritonealFluid,
  Phalanyx: v2_0550_Phalanyx,
  Pilonidal: v2_0550_Pilonidal,
  Pinna: v2_0550_Pinna,
  PlacentaFetalSide: v2_0550_PlacentaFetalSide,
  PlacentaMaternalSide: v2_0550_PlacentaMaternalSide,
  Plantar: v2_0550_Plantar,
  PalateHard: v2_0550_PalateHard,
  PalateSoft: v2_0550_PalateSoft,
  Placenta: v2_0550_Placenta,
  PleuralFluid: v2_0550_PleuralFluid,
  Pleura: v2_0550_Pleura,
  PleuralFluidThoracentesisFld: v2_0550_PleuralFluidThoracentesisFld,
  Perineal: v2_0550_Perineal,
  Perinephric: v2_0550_Perinephric,
  Perineum: v2_0550_Perineum,
  Popliteal: v2_0550_Popliteal,
  Periorbital: v2_0550_Periorbital,
  Preauricular: v2_0550_Preauricular,
  Prerenal: v2_0550_Prerenal,
  ProstaticFluid: v2_0550_ProstaticFluid,
  ProstateGland: v2_0550_ProstateGland,
  Peritonsillar: v2_0550_Peritonsillar,
  Pubic: v2_0550_Pubic,
  PulmonaryArtery: v2_0550_PulmonaryArtery,
  Radial: v2_0550_Radial,
  Radius: v2_0550_Radius,
  RedBloodCells: v2_0550_RedBloodCells,
  Rectal: v2_0550_Rectal,
  Rectum: v2_0550_Rectum,
  Renal: v2_0550_Renal,
  Rib: v2_0550_Rib,
  RenalPelvis: v2_0550_RenalPelvis,
  Retroperitoneal: v2_0550_Retroperitoneal,
  UterineCulDeSac: v2_0550_UterineCulDeSac,
  Sacroiliac: v2_0550_Sacroiliac,
  Sacral: v2_0550_Sacral,
  Sacrococcygeal: v2_0550_Sacrococcygeal,
  Sacrum: v2_0550_Sacrum,
  SalivaryGland: v2_0550_SalivaryGland,
  Scalp: v2_0550_Scalp,
  ScapulaScapular: v2_0550_ScapulaScapular,
  SupraclavicleSupraclavicular: v2_0550_SupraclavicleSupraclavicular,
  Sclera: v2_0550_Sclera,
  SubClavian: v2_0550_SubClavian,
  ScrotumScrotal: v2_0550_ScrotumScrotal,
  Subdiaphramatic: v2_0550_Subdiaphramatic,
  SeminalFluid: v2_0550_SeminalFluid,
  Semen: v2_0550_Semen,
  SeptumSeptal: v2_0550_SeptumSeptal,
  Seroma: v2_0550_Seroma,
  SubgalealFluid: v2_0550_SubgalealFluid,
  Shin: v2_0550_Shin,
  Shoulder: v2_0550_Shoulder,
  SholderJoint: v2_0550_SholderJoint,
  Sigmoid: v2_0550_Sigmoid,
  Sinus: v2_0550_Sinus,
  SkeneQuoteSGland: v2_0550_SkeneQuoteSGland,
  SkeletalMuscle: v2_0550_SkeletalMuscle,
  Skull: v2_0550_Skull,
  Sole: v2_0550_Sole,
  SpinalCord: v2_0550_SpinalCord,
  Sphenoid: v2_0550_Sphenoid,
  Spleen: v2_0550_Spleen,
  Spermatozoa: v2_0550_Spermatozoa,
  SupraCervical: v2_0550_SupraCervical,
  SternumSternal: v2_0550_SternumSternal,
  Stoma: v2_0550_Stoma,
  Stomach: v2_0550_Stomach,
  LiquidStool: v2_0550_LiquidStool,
  Stump: v2_0550_Stump,
  Subdural: v2_0550_Subdural,
  SubduralFluid: v2_0550_SubduralFluid,
  Submandibular: v2_0550_Submandibular,
  Submental: v2_0550_Submental,
  Subphrenic: v2_0550_Subphrenic,
  Submaxillary: v2_0550_Submaxillary,
  SuprapubicSpecimen: v2_0550_SuprapubicSpecimen,
  Suprapubic: v2_0550_Suprapubic,
  Sweat: v2_0550_Sweat,
  SweatGland: v2_0550_SweatGland,
  SynovialFluid: v2_0550_SynovialFluid,
  Synovial: v2_0550_Synovial,
  Synovium: v2_0550_Synovium,
  Tarsal: v2_0550_Tarsal,
  Transbronchial: v2_0550_Transbronchial,
  TranscarinaAsp: v2_0550_TranscarinaAsp,
  TearDuct: v2_0550_TearDuct,
  Tears: v2_0550_Tears,
  Temple: v2_0550_Temple,
  Temporal: v2_0550_Temporal,
  TesticleTestis: v2_0550_TesticleTestis,
  Thigh: v2_0550_Thigh,
  Thymus: v2_0550_Thymus,
  ThoraxThoracicThoracentesis: v2_0550_ThoraxThoracicThoracentesis,
  Throat: v2_0550_Throat,
  Thumb: v2_0550_Thumb,
  Thyroid: v2_0550_Thyroid,
  Tibia: v2_0550_Tibia,
  TemporalLobe: v2_0550_TemporalLobe,
  Thumbnail: v2_0550_Thumbnail,
  Toe: v2_0550_Toe,
  ToeNail: v2_0550_ToeNail,
  Tongue: v2_0550_Tongue,
  Tonsil: v2_0550_Tonsil,
  Tooth: v2_0550_Tooth,
  TracheaTracheal: v2_0550_TracheaTracheal,
  ToothSocket: v2_0550_ToothSocket,
  UlnaUlnar: v2_0550_UlnaUlnar,
  UmbilicalBlood: v2_0550_UmbilicalBlood,
  UmbilicusUmbilical: v2_0550_UmbilicusUmbilical,
  Ureter: v2_0550_Ureter,
  Urethra: v2_0550_Urethra,
  StomaUrinary: v2_0550_StomaUrinary,
  Uterus: v2_0550_Uterus,
  Uterine: v2_0550_Uterine,
  VaginaVaginal: v2_0550_VaginaVaginal,
  Valve: v2_0550_Valve,
  VasDeferens: v2_0550_VasDeferens,
  VastusLateralis: v2_0550_VastusLateralis,
  Vault: v2_0550_Vault,
  VentricularCSF: v2_0550_VentricularCSF,
  VaginalCuff: v2_0550_VaginalCuff,
  Vein: v2_0550_Vein,
  Ventragluteal: v2_0550_Ventragluteal,
  VermisCerebelli: v2_0550_VermisCerebelli,
  VertebraCervical: v2_0550_VertebraCervical,
  VertebraLumbar: v2_0550_VertebraLumbar,
  VertebraThoracic: v2_0550_VertebraThoracic,
  Vesicular: v2_0550_Vesicular,
  VesicularFluid: v2_0550_VesicularFluid,
  Vesicle: v2_0550_Vesicle,
  VestibuleGenital: v2_0550_VestibuleGenital,
  VaginalVault: v2_0550_VaginalVault,
  VitreousFluid: v2_0550_VitreousFluid,
  VocalCord: v2_0550_VocalCord,
  Vulva: v2_0550_Vulva,
  Leukocytes: v2_0550_Leukocytes,
  Wrist: v2_0550_Wrist,
};
const v2_0553_AuthorizationRequestForInpatientAdmission: Coding = {
  code: 'AA',
  display: 'Authorization request for inpatient admission',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_CombinedAuthorizationAndAdjudicationRequest: Coding = {
  code: 'AI',
  display: 'Combined Authorization and Adjudication request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_CancelAuthorizationRequest: Coding = {
  code: 'CA',
  display: 'Cancel Authorization request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_CancelInvoiceProductServiceGroup: Coding = {
  code: 'CG',
  display: 'Cancel Invoice Product/Service Group',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_CancelInvoiceProductServiceLineItem: Coding = {
  code: 'CL',
  display: 'Cancel Invoice Product/Service Line Item',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_CancelInvoice: Coding = {
  code: 'CN',
  display: 'Cancel Invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_CopyOfInvoice: Coding = {
  code: 'CP',
  display: 'Copy of Invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_CoverageRegisterQuery: Coding = {
  code: 'CQ',
  display: 'Coverage Register Query',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_AuthorizationRequestForInpatientStayExtension: Coding = {
  code: 'EA',
  display: 'Authorization request for inpatient stay extension',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_OriginalAuthorization: Coding = {
  code: 'OA',
  display: 'Original Authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_OriginalInvoice: Coding = {
  code: 'OR',
  display: 'Original Invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_PreAuthorization: Coding = {
  code: 'PA',
  display: 'Pre-Authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_PreDeterminationInvoice: Coding = {
  code: 'PD',
  display: 'Pre-Determination Invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_ReAssessment: Coding = {
  code: 'RA',
  display: 'Re-Assessment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_ReferralPreAuthorization: Coding = {
  code: 'RC',
  display: 'Referral Pre-Authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_ReferralAuthorization: Coding = {
  code: 'RU',
  display: 'Referral authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
const v2_0553_SpecialAuthorization: Coding = {
  code: 'SA',
  display: 'Special Authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0553',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0553 ( Invoice Control Code)
 */
export const V20553 = {
  AuthorizationRequestForInpatientAdmission: v2_0553_AuthorizationRequestForInpatientAdmission,
  CombinedAuthorizationAndAdjudicationRequest: v2_0553_CombinedAuthorizationAndAdjudicationRequest,
  CancelAuthorizationRequest: v2_0553_CancelAuthorizationRequest,
  CancelInvoiceProductServiceGroup: v2_0553_CancelInvoiceProductServiceGroup,
  CancelInvoiceProductServiceLineItem: v2_0553_CancelInvoiceProductServiceLineItem,
  CancelInvoice: v2_0553_CancelInvoice,
  CopyOfInvoice: v2_0553_CopyOfInvoice,
  CoverageRegisterQuery: v2_0553_CoverageRegisterQuery,
  AuthorizationRequestForInpatientStayExtension: v2_0553_AuthorizationRequestForInpatientStayExtension,
  OriginalAuthorization: v2_0553_OriginalAuthorization,
  OriginalInvoice: v2_0553_OriginalInvoice,
  PreAuthorization: v2_0553_PreAuthorization,
  PreDeterminationInvoice: v2_0553_PreDeterminationInvoice,
  ReAssessment: v2_0553_ReAssessment,
  ReferralPreAuthorization: v2_0553_ReferralPreAuthorization,
  ReferralAuthorization: v2_0553_ReferralAuthorization,
  SpecialAuthorization: v2_0553_SpecialAuthorization,
};
const v2_0554_LateInvoice: Coding = {
  code: 'LATE',
  display: 'Late Invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0554',
};
const v2_0554_NormalSubmission: Coding = {
  code: 'NORM',
  display: 'Normal submission',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0554',
};
const v2_0554_SubscriberCoverageProblem: Coding = {
  code: 'SUB',
  display: 'Subscriber coverage problem',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0554',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0554 ( Invoice Reason Codes)
 */
export const V20554 = {
  LateInvoice: v2_0554_LateInvoice,
  NormalSubmission: v2_0554_NormalSubmission,
  SubscriberCoverageProblem: v2_0554_SubscriberCoverageProblem,
};
const v2_0555_Block: Coding = {
  code: 'BK',
  display: 'Block',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0555',
};
const v2_0555_Final: Coding = {
  code: 'FN',
  display: 'Final',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0555',
};
const v2_0555_FeeForService: Coding = {
  code: 'FS',
  display: 'Fee for Service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0555',
};
const v2_0555_Group: Coding = {
  code: 'GP',
  display: 'Group',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0555',
};
const v2_0555_InformationOnly: Coding = {
  code: 'IN',
  display: 'Information Only',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0555',
};
const v2_0555_NonPatient: Coding = {
  code: 'NP',
  display: 'Non Patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0555',
};
const v2_0555_Partial: Coding = {
  code: 'PA',
  display: 'Partial',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0555',
};
const v2_0555_Salary: Coding = {
  code: 'SL',
  display: 'Salary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0555',
};
const v2_0555_BySession: Coding = {
  code: 'SS',
  display: 'By Session',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0555',
};
const v2_0555_Supplemental: Coding = {
  code: 'SU',
  display: 'Supplemental',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0555',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0555 ( Invoice Type)
 */
export const V20555 = {
  Block: v2_0555_Block,
  Final: v2_0555_Final,
  FeeForService: v2_0555_FeeForService,
  Group: v2_0555_Group,
  InformationOnly: v2_0555_InformationOnly,
  NonPatient: v2_0555_NonPatient,
  Partial: v2_0555_Partial,
  Salary: v2_0555_Salary,
  BySession: v2_0555_BySession,
  Supplemental: v2_0555_Supplemental,
};
const v2_0556_AMBULATORYCARE: Coding = {
  code: 'AMB',
  display: 'AMBULATORY CARE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0556',
};
const v2_0556_DENTAL: Coding = {
  code: 'DENT',
  display: 'DENTAL',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0556',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0556 ( Benefit Group)
 */
export const V20556 = {
  AMBULATORYCARE: v2_0556_AMBULATORYCARE,
  DENTAL: v2_0556_DENTAL,
};
const v2_0557_Employer: Coding = {
  code: 'EMPL',
  display: 'Employer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0557',
};
const v2_0557_PayeeOrganization: Coding = {
  code: 'ORG',
  display: 'Payee Organization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0557',
};
const v2_0557_Person: Coding = {
  code: 'PERS',
  display: 'Person',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0557',
};
const v2_0557_PayPerson: Coding = {
  code: 'PPER',
  display: 'Pay Person',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0557',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0557 ( Payee Type)
 */
export const V20557 = {
  Employer: v2_0557_Employer,
  PayeeOrganization: v2_0557_PayeeOrganization,
  Person: v2_0557_Person,
  PayPerson: v2_0557_PayPerson,
};
const v2_0558_FamilyMember: Coding = {
  code: 'FM',
  display: 'Family Member',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0558',
};
const v2_0558_Guarantor: Coding = {
  code: 'GT',
  display: 'Guarantor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0558',
};
const v2_0558_Patient: Coding = {
  code: 'PT',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0558',
};
const v2_0558_Subscriber: Coding = {
  code: 'SB',
  display: 'Subscriber',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0558',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0558 ( Payee Relationship to Invoice)
 */
export const V20558 = {
  FamilyMember: v2_0558_FamilyMember,
  Guarantor: v2_0558_Guarantor,
  Patient: v2_0558_Patient,
  Subscriber: v2_0558_Subscriber,
};
const v2_0559_Denied: Coding = {
  code: 'D',
  display: 'Denied',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0559',
};
const v2_0559_Processed: Coding = {
  code: 'P',
  display: 'Processed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0559',
};
const v2_0559_Rejected: Coding = {
  code: 'R',
  display: 'Rejected',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0559',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0559 ( Product/Service Status)
 */
export const V20559 = {
  Denied: v2_0559_Denied,
  Processed: v2_0559_Processed,
  Rejected: v2_0559_Rejected,
};
const v2_0561_ClaimCenter: Coding = {
  code: 'CLCTR',
  display: 'Claim Center',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0561',
};
const v2_0561_DiagnosticApprovalNumber: Coding = {
  code: 'DGAPP',
  display: 'Diagnostic Approval Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0561',
};
const v2_0561_DataCenterNumber: Coding = {
  code: 'DTCTR',
  display: 'Data Center Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0561',
};
const v2_0561_EncounterNumber: Coding = {
  code: 'ENC',
  display: 'Encounter Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0561',
};
const v2_0561_GoodFaithIndicator: Coding = {
  code: 'GFTH',
  display: 'Good Faith Indicator',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0561',
};
const v2_0561_OutOfProvinceIndicator: Coding = {
  code: 'OOP',
  display: 'Out of Province Indicator',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0561',
};
const v2_0561_SequenceNumber: Coding = {
  code: 'SEQ',
  display: 'Sequence Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0561',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0561 ( Product/Services Clarification Codes)
 */
export const V20561 = {
  ClaimCenter: v2_0561_ClaimCenter,
  DiagnosticApprovalNumber: v2_0561_DiagnosticApprovalNumber,
  DataCenterNumber: v2_0561_DataCenterNumber,
  EncounterNumber: v2_0561_EncounterNumber,
  GoodFaithIndicator: v2_0561_GoodFaithIndicator,
  OutOfProvinceIndicator: v2_0561_OutOfProvinceIndicator,
  SequenceNumber: v2_0561_SequenceNumber,
};
const v2_0562_DeferredAdjudicationProcessing: Coding = {
  code: 'DFADJ',
  display: 'Deferred Adjudication Processing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0562',
};
const v2_0562_ElectronicFormToFollow: Coding = {
  code: 'EFORM',
  display: 'Electronic form to follow',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0562',
};
const v2_0562_FaxToFollow: Coding = {
  code: 'FAX',
  display: 'Fax to follow',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0562',
};
const v2_0562_PaperDocumentationToFollow: Coding = {
  code: 'PAPER',
  display: 'Paper documentation to follow',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0562',
};
const v2_0562_DelayedByAPreviousPayer: Coding = {
  code: 'PYRDELAY',
  display: 'Delayed by a Previous Payer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0562',
};
const v2_0562_RealTimeAdjudicationProcessing: Coding = {
  code: 'RTADJ',
  display: 'Real Time Adjudication Processing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0562',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0562 ( Processing Consideration Codes)
 */
export const V20562 = {
  DeferredAdjudicationProcessing: v2_0562_DeferredAdjudicationProcessing,
  ElectronicFormToFollow: v2_0562_ElectronicFormToFollow,
  FaxToFollow: v2_0562_FaxToFollow,
  PaperDocumentationToFollow: v2_0562_PaperDocumentationToFollow,
  DelayedByAPreviousPayer: v2_0562_DelayedByAPreviousPayer,
  RealTimeAdjudicationProcessing: v2_0562_RealTimeAdjudicationProcessing,
};
const v2_0564_EditAdjudicationResponse: Coding = {
  code: 'EA',
  display: 'Edit/Adjudication Response',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0564',
};
const v2_0564_Information: Coding = {
  code: 'IN',
  display: 'Information',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0564',
};
const v2_0564_ProviderAdjustment: Coding = {
  code: 'PA',
  display: 'Provider Adjustment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0564',
};
const v2_0564_ProcessingResult: Coding = {
  code: 'PR',
  display: 'Processing Result',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0564',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0564 ( Adjustment Category Code)
 */
export const V20564 = {
  EditAdjudicationResponse: v2_0564_EditAdjudicationResponse,
  Information: v2_0564_Information,
  ProviderAdjustment: v2_0564_ProviderAdjustment,
  ProcessingResult: v2_0564_ProcessingResult,
};
const v2_0565_DispensingFee: Coding = {
  code: 'DISP',
  display: 'Dispensing Fee',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0565',
};
const v2_0565_GoodsAndServicesTax: Coding = {
  code: 'GST',
  display: 'Goods and Services Tax',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0565',
};
const v2_0565_HarmonizedSalesTax: Coding = {
  code: 'HST',
  display: 'Harmonized Sales Tax',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0565',
};
const v2_0565_MarkUpFee: Coding = {
  code: 'MKUP',
  display: 'Mark up Fee',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0565',
};
const v2_0565_ProvincialSalesTax: Coding = {
  code: 'PST',
  display: 'Provincial Sales Tax',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0565',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0565 ( Provider Adjustment Reason Code)
 */
export const V20565 = {
  DispensingFee: v2_0565_DispensingFee,
  GoodsAndServicesTax: v2_0565_GoodsAndServicesTax,
  HarmonizedSalesTax: v2_0565_HarmonizedSalesTax,
  MarkUpFee: v2_0565_MarkUpFee,
  ProvincialSalesTax: v2_0565_ProvincialSalesTax,
};
const v2_0566_Granulocytes: Coding = {
  code: 'GRN',
  display: 'Granulocytes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0566',
};
const v2_0566_Lymphocytes: Coding = {
  code: 'LYM',
  display: 'Lymphocytes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0566',
};
const v2_0566_Plasma: Coding = {
  code: 'PLS',
  display: 'Plasma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0566',
};
const v2_0566_Platelets: Coding = {
  code: 'PLT',
  display: 'Platelets',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0566',
};
const v2_0566_PeripheralStemCells: Coding = {
  code: 'PSC',
  display: 'Peripheral Stem Cells',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0566',
};
const v2_0566_RedBloodCells: Coding = {
  code: 'RBC',
  display: 'Red Blood Cells',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0566',
};
const v2_0566_WholeBlood: Coding = {
  code: 'WBL',
  display: 'Whole Blood',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0566',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0566 ( Blood Unit Type)
 */
export const V20566 = {
  Granulocytes: v2_0566_Granulocytes,
  Lymphocytes: v2_0566_Lymphocytes,
  Plasma: v2_0566_Plasma,
  Platelets: v2_0566_Platelets,
  PeripheralStemCells: v2_0566_PeripheralStemCells,
  RedBloodCells: v2_0566_RedBloodCells,
  WholeBlood: v2_0566_WholeBlood,
};
const v2_0569_PrintOnEOB: Coding = {
  code: 'EOB',
  display: 'Print on EOB',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0569',
};
const v2_0569_InformPatient: Coding = {
  code: 'PAT',
  display: 'Inform Patient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0569',
};
const v2_0569_InformProvider: Coding = {
  code: 'PRO',
  display: 'Inform Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0569',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0569 ( Adjustment Action)
 */
export const V20569 = {
  PrintOnEOB: v2_0569_PrintOnEOB,
  InformPatient: v2_0569_InformPatient,
  InformProvider: v2_0569_InformProvider,
};
const v2_0570_Cash: Coding = {
  code: 'CASH',
  display: 'Cash',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0570',
};
const v2_0570_CreditCard: Coding = {
  code: 'CCCA',
  display: 'Credit Card',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0570',
};
const v2_0570_CashierQuoteSCheck: Coding = {
  code: 'CCHK',
  display: "Cashier's Check",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0570',
};
const v2_0570_CreditDebitAccount: Coding = {
  code: 'CDAC',
  display: 'Credit/Debit Account',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0570',
};
const v2_0570_Check: Coding = {
  code: 'CHCK',
  display: 'Check',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0570',
};
const v2_0570_DirectDeposit: Coding = {
  code: 'DDPO',
  display: 'Direct Deposit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0570',
};
const v2_0570_DebitCard: Coding = {
  code: 'DEBC',
  display: 'Debit Card',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0570',
};
const v2_0570_SocietyForWorldwideInterbankFinancialTelecommunicationsSWIFT: Coding = {
  code: 'SWFT',
  display: 'Society for Worldwide Interbank Financial Telecommunications (S.W.I.F.T.)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0570',
};
const v2_0570_TravelerQuoteSCheck: Coding = {
  code: 'TRAC',
  display: "Traveler's Check",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0570',
};
const v2_0570_VISASpecialElectronicFundsTransferNetwork: Coding = {
  code: 'VISN',
  display: 'VISA Special Electronic Funds Transfer Network',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0570',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0570 ( Payment Method Code)
 */
export const V20570 = {
  Cash: v2_0570_Cash,
  CreditCard: v2_0570_CreditCard,
  CashierQuoteSCheck: v2_0570_CashierQuoteSCheck,
  CreditDebitAccount: v2_0570_CreditDebitAccount,
  Check: v2_0570_Check,
  DirectDeposit: v2_0570_DirectDeposit,
  DebitCard: v2_0570_DebitCard,
  SocietyForWorldwideInterbankFinancialTelecommunicationsSWIFT: v2_0570_SocietyForWorldwideInterbankFinancialTelecommunicationsSWIFT,
  TravelerQuoteSCheck: v2_0570_TravelerQuoteSCheck,
  VISASpecialElectronicFundsTransferNetwork: v2_0570_VISASpecialElectronicFundsTransferNetwork,
};
const v2_0571_Acknowledge: Coding = {
  code: 'ACK',
  display: 'Acknowledge',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0571',
};
const v2_0571_AdjudicatedWithAdjustments: Coding = {
  code: 'ADJ',
  display: 'Adjudicated with Adjustments',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0571',
};
const v2_0571_AdjudicatedAsSubmitted: Coding = {
  code: 'ADJSUB',
  display: 'Adjudicated as Submitted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0571',
};
const v2_0571_AdjudicatedToZero: Coding = {
  code: 'ADJZER',
  display: 'Adjudicated to Zero',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0571',
};
const v2_0571_Paid: Coding = {
  code: 'PAID',
  display: 'Paid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0571',
};
const v2_0571_Pending: Coding = {
  code: 'PEND',
  display: 'Pending',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0571',
};
const v2_0571_PreDetermination: Coding = {
  code: 'PRED',
  display: 'Pre-Determination',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0571',
};
const v2_0571_Reject: Coding = {
  code: 'REJECT',
  display: 'Reject',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0571',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0571 ( Invoice Processing Results Status)
 */
export const V20571 = {
  Acknowledge: v2_0571_Acknowledge,
  AdjudicatedWithAdjustments: v2_0571_AdjudicatedWithAdjustments,
  AdjudicatedAsSubmitted: v2_0571_AdjudicatedAsSubmitted,
  AdjudicatedToZero: v2_0571_AdjudicatedToZero,
  Paid: v2_0571_Paid,
  Pending: v2_0571_Pending,
  PreDetermination: v2_0571_PreDetermination,
  Reject: v2_0571_Reject,
};
const v2_0572_RegisteredInVATRegister: Coding = {
  code: 'RVAT',
  display: 'Registered in VAT register',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0572',
};
const v2_0572_UnregisteredInVATRegister: Coding = {
  code: 'UVAT',
  display: 'Unregistered in VAT register',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0572',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0572 ( Tax status)
 */
export const V20572 = {
  RegisteredInVATRegister: v2_0572_RegisteredInVATRegister,
  UnregisteredInVATRegister: v2_0572_UnregisteredInVATRegister,
};
const v2_0615_KerberosServiceTicket: Coding = {
  code: 'KERB',
  display: 'Kerberos Service Ticket',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0615',
};
const v2_0615_AuthenticatedUserIdentityAssertion: Coding = {
  code: 'SAML',
  display: 'Authenticated User Identity Assertion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0615',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0615 ( User Authentication Credential Type Code)
 */
export const V20615 = {
  KerberosServiceTicket: v2_0615_KerberosServiceTicket,
  AuthenticatedUserIdentityAssertion: v2_0615_AuthenticatedUserIdentityAssertion,
};
const v2_0616_Corrected: Coding = {
  code: 'C',
  display: 'Corrected',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0616',
};
const v2_0616_AddedInError: Coding = {
  code: 'E',
  display: 'Added in error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0616',
};
const v2_0616_Moved: Coding = {
  code: 'M',
  display: 'Moved',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0616',
};
const v2_0616_OnRequest: Coding = {
  code: 'R',
  display: 'On request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0616',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0616 ( Address Expiration Reason)
 */
export const V20616 = {
  Corrected: v2_0616_Corrected,
  AddedInError: v2_0616_AddedInError,
  Moved: v2_0616_Moved,
  OnRequest: v2_0616_OnRequest,
};
const v2_0617_Classification: Coding = {
  code: 'C',
  display: 'Classification',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0617',
};
const v2_0617_Mailing: Coding = {
  code: 'M',
  display: 'Mailing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0617',
};
const v2_0617_Visit: Coding = {
  code: 'V',
  display: 'Visit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0617',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0617 ( Address Usage)
 */
export const V20617 = {
  Classification: v2_0617_Classification,
  Mailing: v2_0617_Mailing,
  Visit: v2_0617_Visit,
};
const v2_0618_Listed: Coding = {
  code: 'LI',
  display: 'Listed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0618',
};
const v2_0618_UnlistedShouldNotAppearInDirectories: Coding = {
  code: 'UL',
  display: 'Unlisted (Should not appear in directories)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0618',
};
const v2_0618_Unpublished: Coding = {
  code: 'UP',
  display: 'Unpublished',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0618',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0618 ( Protection Code)
 */
export const V20618 = {
  Listed: v2_0618_Listed,
  UnlistedShouldNotAppearInDirectories: v2_0618_UnlistedShouldNotAppearInDirectories,
  Unpublished: v2_0618_Unpublished,
};
const v2_0625_Active: Coding = {
  code: '1',
  display: 'Active',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0625',
};
const v2_0625_PendingInactive: Coding = {
  code: '2',
  display: 'Pending Inactive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0625',
};
const v2_0625_Inactive: Coding = {
  code: '3',
  display: 'Inactive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0625',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0625 ( Item Status Codes)
 */
export const V20625 = {
  Active: v2_0625_Active,
  PendingInactive: v2_0625_PendingInactive,
  Inactive: v2_0625_Inactive,
};
const v2_0634_Critical: Coding = {
  code: 'CRT',
  display: 'Critical',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0634',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0634 ( Item Importance Codes)
 */
export const V20634 = {
  Critical: v2_0634_Critical,
};
const v2_0642_DOPDOQ: Coding = {
  code: 'D',
  display: 'DOP/DOQ',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0642',
};
const v2_0642_MINMAX: Coding = {
  code: 'M',
  display: 'MIN/MAX',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0642',
};
const v2_0642_Override: Coding = {
  code: 'O',
  display: 'Override',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0642',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0642 ( Reorder Theory Codes)
 */
export const V20642 = {
  DOPDOQ: v2_0642_DOPDOQ,
  MINMAX: v2_0642_MINMAX,
  Override: v2_0642_Override,
};
const v2_0651_Cost: Coding = {
  code: 'CST',
  display: 'Cost',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0651',
};
const v2_0651_Time: Coding = {
  code: 'TME',
  display: 'Time',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0651',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0651 ( Labor Calculation Type)
 */
export const V20651 = {
  Cost: v2_0651_Cost,
  Time: v2_0651_Time,
};
const v2_0653_MmDdYy: Coding = {
  code: '1',
  display: 'mm/dd/yy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0653',
};
const v2_0653_YyMmDd: Coding = {
  code: '2',
  display: 'yy.mm.dd',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0653',
};
const v2_0653_DdMmYy: Coding = {
  code: '3',
  display: 'dd/mm/yy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0653',
};
const v2_0653_DdMmYy_2: Coding = {
  code: '4',
  display: 'dd.mm.yy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0653',
};
const v2_0653_YyMmDd_2: Coding = {
  code: '5',
  display: 'yy/mm/dd',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0653',
};
const v2_0653_Yymmdd: Coding = {
  code: '6',
  display: 'Yymmdd',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0653',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0653 ( Date Format)
 */
export const V20653 = {
  MmDdYy: v2_0653_MmDdYy,
  YyMmDd: v2_0653_YyMmDd,
  DdMmYy: v2_0653_DdMmYy,
  DdMmYy_2: v2_0653_DdMmYy_2,
  YyMmDd_2: v2_0653_YyMmDd_2,
  Yymmdd: v2_0653_Yymmdd,
};
const v2_0657_EOGasSterilizer: Coding = {
  code: '1',
  display: 'EO Gas Sterilizer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0657',
};
const v2_0657_SteamSterilizer: Coding = {
  code: '2',
  display: 'Steam Sterilizer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0657',
};
const v2_0657_PeraceticAcid: Coding = {
  code: '3',
  display: 'Peracetic Acid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0657',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0657 ( Device Type)
 */
export const V20657 = {
  EOGasSterilizer: v2_0657_EOGasSterilizer,
  SteamSterilizer: v2_0657_SteamSterilizer,
  PeraceticAcid: v2_0657_PeraceticAcid,
};
const v2_0659_ORModeWithoutOperator: Coding = {
  code: '1',
  display: 'OR Mode Without Operator',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0659',
};
const v2_0659_ORModeWithOperator: Coding = {
  code: '2',
  display: 'OR Mode with Operator',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0659',
};
const v2_0659_CPDModeWithoutOperator: Coding = {
  code: '3',
  display: 'CPD Mode Without Operator',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0659',
};
const v2_0659_CPDModeWithOperator: Coding = {
  code: '4',
  display: 'CPD Mode With Operator',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0659',
};
const v2_0659_OfflineMode: Coding = {
  code: '5',
  display: 'Offline Mode',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0659',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0659 ( Lot Control)
 */
export const V20659 = {
  ORModeWithoutOperator: v2_0659_ORModeWithoutOperator,
  ORModeWithOperator: v2_0659_ORModeWithOperator,
  CPDModeWithoutOperator: v2_0659_CPDModeWithoutOperator,
  CPDModeWithOperator: v2_0659_CPDModeWithOperator,
  OfflineMode: v2_0659_OfflineMode,
};
const v2_0667_RealTimeValues: Coding = {
  code: '0',
  display: 'Real Time Values',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0667',
};
const v2_0667_HistoricValues: Coding = {
  code: '1',
  display: 'Historic Values',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0667',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0667 ( Device Data State)
 */
export const V20667 = {
  RealTimeValues: v2_0667_RealTimeValues,
  HistoricValues: v2_0667_HistoricValues,
};
const v2_0669_LoadIsComplete: Coding = {
  code: 'LCC',
  display: 'Load is Complete',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0669',
};
const v2_0669_LoadCanceled: Coding = {
  code: 'LCN',
  display: 'Load Canceled',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0669',
};
const v2_0669_LoadInProcess: Coding = {
  code: 'LCP',
  display: 'Load In Process',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0669',
};
const v2_0669_BuildingALoad: Coding = {
  code: 'LLD',
  display: 'Building a Load',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0669',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0669 ( Load Status)
 */
export const V20669 = {
  LoadIsComplete: v2_0669_LoadIsComplete,
  LoadCanceled: v2_0669_LoadCanceled,
  LoadInProcess: v2_0669_LoadInProcess,
  BuildingALoad: v2_0669_BuildingALoad,
};
const v2_0682_Ready: Coding = {
  code: '0',
  display: 'Ready',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0682',
};
const v2_0682_NotReady: Coding = {
  code: '1',
  display: 'Not Ready',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0682',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0682 ( Device Status)
 */
export const V20682 = {
  Ready: v2_0682_Ready,
  NotReady: v2_0682_NotReady,
};
const v2_0702_SecondRinse: Coding = {
  code: '2RS',
  display: 'Second Rinse',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_AnesthesiaRespiratory: Coding = {
  code: 'ANR',
  display: 'Anesthesia/Respiratory',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Bedpans: Coding = {
  code: 'BDP',
  display: 'Bedpans',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_BowieDickTest: Coding = {
  code: 'BWD',
  display: 'Bowie-Dick Test',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_ChemicalWash: Coding = {
  code: 'CMW',
  display: 'Chemical Wash',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Code: Coding = {
  code: 'COD',
  display: 'Code',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_CartWash: Coding = {
  code: 'CRT',
  display: 'Cart Wash',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Decontamination: Coding = {
  code: 'DEC',
  display: 'Decontamination',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Dart: Coding = {
  code: 'DRT',
  display: 'Dart',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_DartWarmUpCycle: Coding = {
  code: 'DRW',
  display: 'Dart Warm-up Cycle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_EOHighTemperature: Coding = {
  code: 'EOH',
  display: 'EO High Temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_EOLowTemperature: Coding = {
  code: 'EOL',
  display: 'EO Low Temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Express: Coding = {
  code: 'EXP',
  display: 'Express',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Flash: Coding = {
  code: 'FLS',
  display: 'Flash',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Glassware: Coding = {
  code: 'GLS',
  display: 'Glassware',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_GenPurpose: Coding = {
  code: 'GNP',
  display: 'Gen. Purpose',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Gravity: Coding = {
  code: 'GRV',
  display: 'Gravity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Gentle: Coding = {
  code: 'GTL',
  display: 'Gentle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Isothermal: Coding = {
  code: 'ISO',
  display: 'Isothermal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_InstrumentWash: Coding = {
  code: 'IST',
  display: 'Instrument Wash',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_LeakTest: Coding = {
  code: 'LKT',
  display: 'Leak Test',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Liquid: Coding = {
  code: 'LQD',
  display: 'Liquid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_OptionalWash: Coding = {
  code: 'OPW',
  display: 'Optional Wash',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_PeraceticAcid: Coding = {
  code: 'PEA',
  display: 'Peracetic Acid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_PlasticGoodsWash: Coding = {
  code: 'PLA',
  display: 'Plastic Goods Wash',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Prevac: Coding = {
  code: 'PRV',
  display: 'Prevac',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Rinse: Coding = {
  code: 'RNS',
  display: 'Rinse',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_SteamFlushPressurePulse: Coding = {
  code: 'SFP',
  display: 'Steam Flush Pressure Pulse',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_Thermal: Coding = {
  code: 'THR',
  display: 'Thermal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_TrayBasin: Coding = {
  code: 'TRB',
  display: 'Tray/Basin',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_UtensilWash: Coding = {
  code: 'UTL',
  display: 'Utensil Wash',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
const v2_0702_WrapSteamFlushPressurePulseWrapSFPP: Coding = {
  code: 'WFP',
  display: 'Wrap/Steam Flush Pressure Pulse (Wrap/SFPP)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0702',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0702 ( Cycle Type)
 */
export const V20702 = {
  SecondRinse: v2_0702_SecondRinse,
  AnesthesiaRespiratory: v2_0702_AnesthesiaRespiratory,
  Bedpans: v2_0702_Bedpans,
  BowieDickTest: v2_0702_BowieDickTest,
  ChemicalWash: v2_0702_ChemicalWash,
  Code: v2_0702_Code,
  CartWash: v2_0702_CartWash,
  Decontamination: v2_0702_Decontamination,
  Dart: v2_0702_Dart,
  DartWarmUpCycle: v2_0702_DartWarmUpCycle,
  EOHighTemperature: v2_0702_EOHighTemperature,
  EOLowTemperature: v2_0702_EOLowTemperature,
  Express: v2_0702_Express,
  Flash: v2_0702_Flash,
  Glassware: v2_0702_Glassware,
  GenPurpose: v2_0702_GenPurpose,
  Gravity: v2_0702_Gravity,
  Gentle: v2_0702_Gentle,
  Isothermal: v2_0702_Isothermal,
  InstrumentWash: v2_0702_InstrumentWash,
  LeakTest: v2_0702_LeakTest,
  Liquid: v2_0702_Liquid,
  OptionalWash: v2_0702_OptionalWash,
  PeraceticAcid: v2_0702_PeraceticAcid,
  PlasticGoodsWash: v2_0702_PlasticGoodsWash,
  Prevac: v2_0702_Prevac,
  Rinse: v2_0702_Rinse,
  SteamFlushPressurePulse: v2_0702_SteamFlushPressurePulse,
  Thermal: v2_0702_Thermal,
  TrayBasin: v2_0702_TrayBasin,
  UtensilWash: v2_0702_UtensilWash,
  WrapSteamFlushPressurePulseWrapSFPP: v2_0702_WrapSteamFlushPressurePulseWrapSFPP,
};
const v2_0717_All: Coding = {
  code: 'ALL',
  display: 'All',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0717',
};
const v2_0717_AllDemographicData: Coding = {
  code: 'DEM',
  display: 'All demographic data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0717',
};
const v2_0717_Drug: Coding = {
  code: 'DRG',
  display: 'Drug',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0717',
};
const v2_0717_HIVStatusAndResults: Coding = {
  code: 'HIV',
  display: 'HIV status and results',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0717',
};
const v2_0717_PatientLocation: Coding = {
  code: 'LOC',
  display: 'Patient Location',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0717',
};
const v2_0717_None: Coding = {
  code: 'NO',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0717',
};
const v2_0717_OptInAllRegistriesHIPAA: Coding = {
  code: 'OI',
  display: 'Opt in all registries (HIPAA)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0717',
};
const v2_0717_OptOutAllRegistriesHIPAA: Coding = {
  code: 'OO',
  display: 'Opt out all registries (HIPAA)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0717',
};
const v2_0717_Religion: Coding = {
  code: 'PID-17',
  display: 'Religion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0717',
};
const v2_0717_DateOfBirth: Coding = {
  code: 'PID-7',
  display: 'Date of Birth',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0717',
};
const v2_0717_PsychiatricMentalHealth: Coding = {
  code: 'PSY',
  display: 'Psychiatric Mental health',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0717',
};
const v2_0717_SensitiveMedicalData: Coding = {
  code: 'SMD',
  display: 'Sensitive medical data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0717',
};
const v2_0717_SexuallyTransmittedDiseases: Coding = {
  code: 'STD',
  display: 'Sexually transmitted diseases',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0717',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0717 ( Access Restriction Value)
 */
export const V20717 = {
  All: v2_0717_All,
  AllDemographicData: v2_0717_AllDemographicData,
  Drug: v2_0717_Drug,
  HIVStatusAndResults: v2_0717_HIVStatusAndResults,
  PatientLocation: v2_0717_PatientLocation,
  None: v2_0717_None,
  OptInAllRegistriesHIPAA: v2_0717_OptInAllRegistriesHIPAA,
  OptOutAllRegistriesHIPAA: v2_0717_OptOutAllRegistriesHIPAA,
  Religion: v2_0717_Religion,
  DateOfBirth: v2_0717_DateOfBirth,
  PsychiatricMentalHealth: v2_0717_PsychiatricMentalHealth,
  SensitiveMedicalData: v2_0717_SensitiveMedicalData,
  SexuallyTransmittedDiseases: v2_0717_SexuallyTransmittedDiseases,
};
const v2_0725_Appointment: Coding = {
  code: 'APT',
  display: 'Appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0725',
};
const v2_0725_AppointmentRequest: Coding = {
  code: 'ARQ',
  display: 'Appointment Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0725',
};
const v2_0725_Event: Coding = {
  code: 'EVN',
  display: 'Event',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0725',
};
const v2_0725_EventCriterion: Coding = {
  code: 'EVN.CRT',
  display: 'Event Criterion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0725',
};
const v2_0725_Expectation: Coding = {
  code: 'EXP',
  display: 'Expectation',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0725',
};
const v2_0725_Intent: Coding = {
  code: 'INT',
  display: 'Intent',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0725',
};
const v2_0725_Promise: Coding = {
  code: 'PRMS',
  display: 'Promise',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0725',
};
const v2_0725_Proposal: Coding = {
  code: 'PRP',
  display: 'Proposal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0725',
};
const v2_0725_RequestOrder: Coding = {
  code: 'RQO',
  display: 'Request-Order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0725',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0725 ( Mood Codes)
 */
export const V20725 = {
  Appointment: v2_0725_Appointment,
  AppointmentRequest: v2_0725_AppointmentRequest,
  Event: v2_0725_Event,
  EventCriterion: v2_0725_EventCriterion,
  Expectation: v2_0725_Expectation,
  Intent: v2_0725_Intent,
  Promise: v2_0725_Promise,
  Proposal: v2_0725_Proposal,
  RequestOrder: v2_0725_RequestOrder,
};
const v2_0728_NothingObvious: Coding = {
  code: '0',
  display: 'Nothing obvious',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0728',
};
const v2_0728_Low: Coding = {
  code: '1',
  display: 'Low',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0728',
};
const v2_0728_Moderate: Coding = {
  code: '2',
  display: 'Moderate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0728',
};
const v2_0728_High: Coding = {
  code: '3',
  display: 'High',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0728',
};
const v2_0728_VeryHigh: Coding = {
  code: '4',
  display: 'Very high',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0728',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0728 ( CCL Value)
 */
export const V20728 = {
  NothingObvious: v2_0728_NothingObvious,
  Low: v2_0728_Low,
  Moderate: v2_0728_Moderate,
  High: v2_0728_High,
  VeryHigh: v2_0728_VeryHigh,
};
const v2_0731_ValidCode: Coding = {
  code: '0',
  display: 'Valid code',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0731',
};
const v2_0731_InvalidCode: Coding = {
  code: '1',
  display: 'Invalid code',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0731',
};
const v2_0731_TwoPrimaryDiagnosisCodes: Coding = {
  code: '2',
  display: 'Two primary diagnosis codes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0731',
};
const v2_0731_InvalidForThisGender: Coding = {
  code: '3',
  display: 'Invalid for this gender',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0731',
};
const v2_0731_InvalidForThisAge: Coding = {
  code: '4',
  display: 'Invalid for this age',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0731',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0731 ( DRG Diagnosis Determination Status)
 */
export const V20731 = {
  ValidCode: v2_0731_ValidCode,
  InvalidCode: v2_0731_InvalidCode,
  TwoPrimaryDiagnosisCodes: v2_0731_TwoPrimaryDiagnosisCodes,
  InvalidForThisGender: v2_0731_InvalidForThisGender,
  InvalidForThisAge: v2_0731_InvalidForThisAge,
};
const v2_0734_NormalGrouping: Coding = {
  code: '0',
  display: 'Normal grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0734',
};
const v2_0734_InvalidOrMissingPrimaryDiagnosis: Coding = {
  code: '1',
  display: 'Invalid or missing primary diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0734',
};
const v2_0734_DiagnosisIsNotAllowedToBePrimary: Coding = {
  code: '2',
  display: 'Diagnosis is not allowed to be primary',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0734',
};
const v2_0734_DataDoesNotFulfillDRGCriteria: Coding = {
  code: '3',
  display: 'Data does not fulfill DRG criteria',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0734',
};
const v2_0734_InvalidAgeAdmissionDateDateOfBirthOrDischargeDate: Coding = {
  code: '4',
  display: 'Invalid age, admission date, date of birth or discharge date',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0734',
};
const v2_0734_InvalidGender: Coding = {
  code: '5',
  display: 'Invalid gender',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0734',
};
const v2_0734_InvalidDischargeStatus: Coding = {
  code: '6',
  display: 'Invalid discharge status',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0734',
};
const v2_0734_InvalidWeightAdAdmission: Coding = {
  code: '7',
  display: 'Invalid weight ad admission',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0734',
};
const v2_0734_InvalidLengthOfStay: Coding = {
  code: '8',
  display: 'Invalid length of stay',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0734',
};
const v2_0734_InvalidFieldQuotationSameDayQuotation: Coding = {
  code: '9',
  display: 'Invalid field "same day"',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0734',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0734 ( Grouper Status)
 */
export const V20734 = {
  NormalGrouping: v2_0734_NormalGrouping,
  InvalidOrMissingPrimaryDiagnosis: v2_0734_InvalidOrMissingPrimaryDiagnosis,
  DiagnosisIsNotAllowedToBePrimary: v2_0734_DiagnosisIsNotAllowedToBePrimary,
  DataDoesNotFulfillDRGCriteria: v2_0734_DataDoesNotFulfillDRGCriteria,
  InvalidAgeAdmissionDateDateOfBirthOrDischargeDate: v2_0734_InvalidAgeAdmissionDateDateOfBirthOrDischargeDate,
  InvalidGender: v2_0734_InvalidGender,
  InvalidDischargeStatus: v2_0734_InvalidDischargeStatus,
  InvalidWeightAdAdmission: v2_0734_InvalidWeightAdAdmission,
  InvalidLengthOfStay: v2_0734_InvalidLengthOfStay,
  InvalidFieldQuotationSameDayQuotation: v2_0734_InvalidFieldQuotationSameDayQuotation,
};
const v2_0739_NormalLengthOfStay: Coding = {
  code: '1',
  display: 'Normal length of stay',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0739',
};
const v2_0739_ShortLengthOfStay: Coding = {
  code: '2',
  display: 'Short length of stay',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0739',
};
const v2_0739_LongLengthOfStay: Coding = {
  code: '3',
  display: 'Long length of stay',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0739',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0739 ( Status Patient)
 */
export const V20739 = {
  NormalLengthOfStay: v2_0739_NormalLengthOfStay,
  ShortLengthOfStay: v2_0739_ShortLengthOfStay,
  LongLengthOfStay: v2_0739_LongLengthOfStay,
};
const v2_0742_EffectiveWeightCalculated: Coding = {
  code: '00',
  display: 'Effective weight calculated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0742',
};
const v2_0742_HospitalSpecificContract: Coding = {
  code: '01',
  display: 'Hospital specific contract',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0742',
};
const v2_0742_EeffectiveWeightForTransferReferralCalculated: Coding = {
  code: '03',
  display: 'Eeffective weight for transfer/referral calculated',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0742',
};
const v2_0742_ReferralFromOtherHospitalBasedOnACooperationNoDRGReimbursement: Coding = {
  code: '04',
  display: 'Referral from other hospital based on a cooperation (no DRG reimbursement)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0742',
};
const v2_0742_InvalidLengthOfStay: Coding = {
  code: '05',
  display: 'Invalid length of stay',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0742',
};
const v2_0742_NoInformationEntryInCostDataForThisDRG: Coding = {
  code: '10',
  display: 'No information/entry in cost data for this DRG',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0742',
};
const v2_0742_NoRelativeWeightFoundForDepartmentType: Coding = {
  code: '11',
  display: 'No relative weight found for department (type)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0742',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0742 ( DRG Status Financial Calculation)
 */
export const V20742 = {
  EffectiveWeightCalculated: v2_0742_EffectiveWeightCalculated,
  HospitalSpecificContract: v2_0742_HospitalSpecificContract,
  EeffectiveWeightForTransferReferralCalculated: v2_0742_EeffectiveWeightForTransferReferralCalculated,
  ReferralFromOtherHospitalBasedOnACooperationNoDRGReimbursement: v2_0742_ReferralFromOtherHospitalBasedOnACooperationNoDRGReimbursement,
  InvalidLengthOfStay: v2_0742_InvalidLengthOfStay,
  NoInformationEntryInCostDataForThisDRG: v2_0742_NoInformationEntryInCostDataForThisDRG,
  NoRelativeWeightFoundForDepartmentType: v2_0742_NoRelativeWeightFoundForDepartmentType,
};
const v2_0749_ValidCodeNotUsedForGrouping: Coding = {
  code: '0',
  display: 'Valid code; not used for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0749',
};
const v2_0749_ValidCodeUsedForGrouping: Coding = {
  code: '1',
  display: 'Valid code; used for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0749',
};
const v2_0749_InvalidCodeNotUsedForGrouping: Coding = {
  code: '2',
  display: 'Invalid code; not used for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0749',
};
const v2_0749_InvalidCodeCodeIsRelevantForGrouping: Coding = {
  code: '3',
  display: 'Invalid code; code is relevant for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0749',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0749 ( DRG Grouping Status)
 */
export const V20749 = {
  ValidCodeNotUsedForGrouping: v2_0749_ValidCodeNotUsedForGrouping,
  ValidCodeUsedForGrouping: v2_0749_ValidCodeUsedForGrouping,
  InvalidCodeNotUsedForGrouping: v2_0749_InvalidCodeNotUsedForGrouping,
  InvalidCodeCodeIsRelevantForGrouping: v2_0749_InvalidCodeCodeIsRelevantForGrouping,
};
const v2_0755_NoWeightReportedAtAdmissionUsedForGrouping: Coding = {
  code: '0',
  display: 'No weight reported at admission used for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0755',
};
const v2_0755_WeightReportedAtAdmissionUsedForGrouping: Coding = {
  code: '1',
  display: 'Weight reported at admission used for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0755',
};
const v2_0755_DefaultWeightGreaterThan2499gUsedForGrouping: Coding = {
  code: '2',
  display: 'Default weight (>2499g) used for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0755',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0755 ( Status Weight At Birth)
 */
export const V20755 = {
  NoWeightReportedAtAdmissionUsedForGrouping: v2_0755_NoWeightReportedAtAdmissionUsedForGrouping,
  WeightReportedAtAdmissionUsedForGrouping: v2_0755_WeightReportedAtAdmissionUsedForGrouping,
  DefaultWeightGreaterThan2499gUsedForGrouping: v2_0755_DefaultWeightGreaterThan2499gUsedForGrouping,
};
const v2_0757_RespirationMinutesNotUsedForGrouping: Coding = {
  code: '0',
  display: 'Respiration minutes not used for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0757',
};
const v2_0757_ListedRespirationMinutesUsedForGrouping: Coding = {
  code: '1',
  display: 'Listed respiration minutes used for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0757',
};
const v2_0757_OPSCodeValueUsedForGrouping: Coding = {
  code: '2',
  display: 'OPS code value used for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0757',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0757 ( Status Respiration Minutes)
 */
export const V20757 = {
  RespirationMinutesNotUsedForGrouping: v2_0757_RespirationMinutesNotUsedForGrouping,
  ListedRespirationMinutesUsedForGrouping: v2_0757_ListedRespirationMinutesUsedForGrouping,
  OPSCodeValueUsedForGrouping: v2_0757_OPSCodeValueUsedForGrouping,
};
const v2_0759_AdmissionStatusIsValidUsedForGrouping: Coding = {
  code: '0',
  display: 'Admission status is valid; used for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0759',
};
const v2_0759_AdmissionStatusIsValidNotUsedForGrouping: Coding = {
  code: '1',
  display: 'Admission status is valid; not used for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0759',
};
const v2_0759_AdmissionStatusIsInvalidNotUsedForGrouping: Coding = {
  code: '2',
  display: 'Admission status is invalid; not used for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0759',
};
const v2_0759_AdmissionStatusIsInvalidDefaultValueUsedForGrouping: Coding = {
  code: '3',
  display: 'Admission status is invalid; default value used for grouping',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0759',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0759 ( Status Admission)
 */
export const V20759 = {
  AdmissionStatusIsValidUsedForGrouping: v2_0759_AdmissionStatusIsValidUsedForGrouping,
  AdmissionStatusIsValidNotUsedForGrouping: v2_0759_AdmissionStatusIsValidNotUsedForGrouping,
  AdmissionStatusIsInvalidNotUsedForGrouping: v2_0759_AdmissionStatusIsInvalidNotUsedForGrouping,
  AdmissionStatusIsInvalidDefaultValueUsedForGrouping: v2_0759_AdmissionStatusIsInvalidDefaultValueUsedForGrouping,
};
const v2_0761_ValidCode: Coding = {
  code: '0',
  display: 'Valid code',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0761',
};
const v2_0761_InvalidCode: Coding = {
  code: '1',
  display: 'Invalid code',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0761',
};
const v2_0761_NotUsed: Coding = {
  code: '2',
  display: 'Not used',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0761',
};
const v2_0761_InvalidForThisGender: Coding = {
  code: '3',
  display: 'Invalid for this gender',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0761',
};
const v2_0761_InvalidForThisAge: Coding = {
  code: '4',
  display: 'Invalid for this age',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0761',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0761 ( DRG Procedure Determination Status)
 */
export const V20761 = {
  ValidCode: v2_0761_ValidCode,
  InvalidCode: v2_0761_InvalidCode,
  NotUsed: v2_0761_NotUsed,
  InvalidForThisGender: v2_0761_InvalidForThisGender,
  InvalidForThisAge: v2_0761_InvalidForThisAge,
};
const v2_0763_NeitherOperationRelevantNorNonOperationRelevantProcedure: Coding = {
  code: '0',
  display: 'Neither operation relevant nor non-operation relevant procedure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0763',
};
const v2_0763_OperationRelevantProcedure: Coding = {
  code: '1',
  display: 'Operation relevant procedure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0763',
};
const v2_0763_NonOperationRelevantProcedure: Coding = {
  code: '2',
  display: 'Non-operation relevant procedure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0763',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0763 ( DRG Procedure Relevance)
 */
export const V20763 = {
  NeitherOperationRelevantNorNonOperationRelevantProcedure: v2_0763_NeitherOperationRelevantNorNonOperationRelevantProcedure,
  OperationRelevantProcedure: v2_0763_OperationRelevantProcedure,
  NonOperationRelevantProcedure: v2_0763_NonOperationRelevantProcedure,
};
const v2_0776_Active: Coding = {
  code: 'A',
  display: 'Active',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0776',
};
const v2_0776_Inactive: Coding = {
  code: 'I',
  display: 'Inactive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0776',
};
const v2_0776_PendingInactive: Coding = {
  code: 'P',
  display: 'Pending Inactive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0776',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0776 ( Item Status)
 */
export const V20776 = {
  Active: v2_0776_Active,
  Inactive: v2_0776_Inactive,
  PendingInactive: v2_0776_PendingInactive,
};
const v2_0778_Equipment: Coding = {
  code: 'EQP',
  display: 'Equipment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0778',
};
const v2_0778_Implant: Coding = {
  code: 'IMP',
  display: 'Implant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0778',
};
const v2_0778_Medication: Coding = {
  code: 'MED',
  display: 'Medication',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0778',
};
const v2_0778_Supply: Coding = {
  code: 'SUP',
  display: 'Supply',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0778',
};
const v2_0778_TubesDrainsAndCatheters: Coding = {
  code: 'TDC',
  display: 'Tubes, Drains, and Catheters',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0778',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0778 ( Item Type)
 */
export const V20778 = {
  Equipment: v2_0778_Equipment,
  Implant: v2_0778_Implant,
  Medication: v2_0778_Medication,
  Supply: v2_0778_Supply,
  TubesDrainsAndCatheters: v2_0778_TubesDrainsAndCatheters,
};
const v2_0790_AmericanMedicalAssociation: Coding = {
  code: 'AMA',
  display: 'American Medical Association',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0790',
};
const v2_0790_FoodAndDrugAdministration: Coding = {
  code: 'FDA',
  display: 'Food and Drug Administration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0790',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0790 ( Approving Regulatory Agency)
 */
export const V20790 = {
  AmericanMedicalAssociation: v2_0790_AmericanMedicalAssociation,
  FoodAndDrugAdministration: v2_0790_FoodAndDrugAdministration,
};
const v2_0793_SafeMedicalDevicesAct: Coding = {
  code: 'SMDA',
  display: 'Safe Medical Devices Act',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0793',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0793 ( Ruling Act)
 */
export const V20793 = {
  SafeMedicalDevicesAct: v2_0793_SafeMedicalDevicesAct,
};
const v2_0806_EthyleneOxideGas: Coding = {
  code: 'EOG',
  display: 'Ethylene Oxide Gas',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0806',
};
const v2_0806_PeraceticAcid: Coding = {
  code: 'PCA',
  display: 'Peracetic acid',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0806',
};
const v2_0806_Steam: Coding = {
  code: 'STM',
  display: 'Steam',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0806',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0806 ( Sterilization Type)
 */
export const V20806 = {
  EthyleneOxideGas: v2_0806_EthyleneOxideGas,
  PeraceticAcid: v2_0806_PeraceticAcid,
  Steam: v2_0806_Steam,
};
const v2_0818_Box: Coding = {
  code: 'BX',
  display: 'Box',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0818',
};
const v2_0818_Case: Coding = {
  code: 'CS',
  display: 'Case',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0818',
};
const v2_0818_Each: Coding = {
  code: 'EA',
  display: 'Each',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0818',
};
const v2_0818_Set: Coding = {
  code: 'SET',
  display: 'Set',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0818',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0818 ( Package)
 */
export const V20818 = {
  Box: v2_0818_Box,
  Case: v2_0818_Case,
  Each: v2_0818_Each,
  Set: v2_0818_Set,
};
const v2_0834_ApplicationData: Coding = {
  code: 'application',
  display: 'Application data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0834',
};
const v2_0834_AudioData: Coding = {
  code: 'audio',
  display: 'Audio data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0834',
};
const v2_0834_ImageData: Coding = {
  code: 'image',
  display: 'Image data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0834',
};
const v2_0834_ModelData: Coding = {
  code: 'model',
  display: 'Model data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0834',
};
const v2_0834_MIMEMultipartPackage: Coding = {
  code: 'multipart',
  display: 'MIME multipart package',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0834',
};
const v2_0834_TextData: Coding = {
  code: 'text',
  display: 'Text data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0834',
};
const v2_0834_VideoData: Coding = {
  code: 'video',
  display: 'Video data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0834',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0834 ( MIME Types)
 */
export const V20834 = {
  ApplicationData: v2_0834_ApplicationData,
  AudioData: v2_0834_AudioData,
  ImageData: v2_0834_ImageData,
  ModelData: v2_0834_ModelData,
  MIMEMultipartPackage: v2_0834_MIMEMultipartPackage,
  TextData: v2_0834_TextData,
  VideoData: v2_0834_VideoData,
};
const v2_0868_Corrected: Coding = {
  code: 'C',
  display: 'Corrected',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0868',
};
const v2_0868_AddedInError: Coding = {
  code: 'E',
  display: 'Added in error',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0868',
};
const v2_0868_Moved: Coding = {
  code: 'M',
  display: 'Moved',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0868',
};
const v2_0868_NoLongerInService: Coding = {
  code: 'N',
  display: 'No longer in service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0868',
};
const v2_0868_OnRequest: Coding = {
  code: 'R',
  display: 'On request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0868',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0868 ( Telecommunication expiration reason)
 */
export const V20868 = {
  Corrected: v2_0868_Corrected,
  AddedInError: v2_0868_AddedInError,
  Moved: v2_0868_Moved,
  NoLongerInService: v2_0868_NoLongerInService,
  OnRequest: v2_0868_OnRequest,
};
const v2_0871_Corrosive: Coding = {
  code: 'COR',
  display: 'Corrosive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0871',
};
const v2_0871_Explosive: Coding = {
  code: 'EXP',
  display: 'Explosive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0871',
};
const v2_0871_Flammable: Coding = {
  code: 'FLA',
  display: 'Flammable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0871',
};
const v2_0871_InjuryHazard: Coding = {
  code: 'INJ',
  display: 'Injury Hazard',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0871',
};
const v2_0871_Radioactive: Coding = {
  code: 'RAD',
  display: 'Radioactive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0871',
};
const v2_0871_Toxic: Coding = {
  code: 'TOX',
  display: 'Toxic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0871',
};
const v2_0871_Unknown: Coding = {
  code: 'UNK',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0871',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0871 ( Supply Risk Codes)
 */
export const V20871 = {
  Corrosive: v2_0871_Corrosive,
  Explosive: v2_0871_Explosive,
  Flammable: v2_0871_Flammable,
  InjuryHazard: v2_0871_InjuryHazard,
  Radioactive: v2_0871_Radioactive,
  Toxic: v2_0871_Toxic,
  Unknown: v2_0871_Unknown,
};
const v2_0881_Both: Coding = {
  code: 'B',
  display: 'Both',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0881',
};
const v2_0881_ProfessionalPart: Coding = {
  code: 'P',
  display: 'Professional Part',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0881',
};
const v2_0881_TechnicalPart: Coding = {
  code: 'T',
  display: 'Technical Part',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0881',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0881 ( Role Executing Physician)
 */
export const V20881 = {
  Both: v2_0881_Both,
  ProfessionalPart: v2_0881_ProfessionalPart,
  TechnicalPart: v2_0881_TechnicalPart,
};
const v2_0882_Employed: Coding = {
  code: 'E',
  display: 'Employed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0882',
};
const v2_0882_SelfEmployed: Coding = {
  code: 'SE',
  display: 'Self-employed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0882',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0882 ( Medical Role Executing Physician)
 */
export const V20882 = {
  Employed: v2_0882_Employed,
  SelfEmployed: v2_0882_SelfEmployed,
};
const v2_0894_Left: Coding = {
  code: 'L',
  display: 'Left',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0894',
};
const v2_0894_Right: Coding = {
  code: 'R',
  display: 'Right',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0894',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0894 ( Side of body)
 */
export const V20894 = {
  Left: v2_0894_Left,
  Right: v2_0894_Right,
};
const v2_0904_BankCardValidationNumber: Coding = {
  code: 'BCV',
  display: 'Bank Card Validation Number',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0904',
};
const v2_0904_CreditCardSecurityCode: Coding = {
  code: 'CCS',
  display: 'Credit Card Security code',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0904',
};
const v2_0904_VersionID: Coding = {
  code: 'VID',
  display: 'Version ID',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0904',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0904 ( Security Check Scheme)
 */
export const V20904 = {
  BankCardValidationNumber: v2_0904_BankCardValidationNumber,
  CreditCardSecurityCode: v2_0904_CreditCardSecurityCode,
  VersionID: v2_0904_VersionID,
};
const v2_0905_Inventoried: Coding = {
  code: 'INV',
  display: 'Inventoried',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0905',
};
const v2_0905_OnHold: Coding = {
  code: 'ONH',
  display: 'On Hold',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0905',
};
const v2_0905_Processing: Coding = {
  code: 'PRC',
  display: 'Processing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0905',
};
const v2_0905_Rejected: Coding = {
  code: 'REJ',
  display: 'Rejected',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0905',
};
const v2_0905_InTransit: Coding = {
  code: 'TRN',
  display: 'In Transit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0905',
};
const v2_0905_TriagedToLab: Coding = {
  code: 'TTL',
  display: 'Triaged to Lab',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0905',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0905 ( Shipment Status)
 */
export const V20905 = {
  Inventoried: v2_0905_Inventoried,
  OnHold: v2_0905_OnHold,
  Processing: v2_0905_Processing,
  Rejected: v2_0905_Rejected,
  InTransit: v2_0905_InTransit,
  TriagedToLab: v2_0905_TriagedToLab,
};
const v2_0906_ASAPAsSoonAsPossibleNextHighestPriorityAfterStat: Coding = {
  code: 'A',
  display: 'ASAP - As soon as possible, next highest priority after stat',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_CallbackResultsFillerShouldContactThePlacerAsSoonAsResultsAreAvailableEvenForPreliminaryResults: Coding = {
  code: 'CR',
  display: 'Callback results - filler should contact the placer as soon as results are available, even for preliminary results',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_CallbackForSchedulingFillerShouldContactThePlacerOrTargetToScheduleTheService: Coding = {
  code: 'CS',
  display: 'Callback for scheduling - Filler should contact the placer (or target) to schedule the service.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_CallbackPlacerForSchedulingFillerShouldContactThePlacerToScheduleTheService: Coding = {
  code: 'CSP',
  display: 'Callback placer for scheduling - filler should contact the placer to schedule the service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_ContactRecipientForSchedulingFillerShouldContactTheServiceRecipientTargetToScheduleTheService: Coding = {
  code: 'CSR',
  display: 'Contact recipient for scheduling - Filler should contact the service recipient (target) to schedule the service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_ElectiveBeneficialToThePatientButNotEssentialForSurvival: Coding = {
  code: 'EL',
  display: 'Elective - Beneficial to the patient but not essential for survival.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_EmergencyAnUnforeseenCombinationOfCircumstancesOrTheResultingStateThatCallsForImmediateAction: Coding = {
  code: 'EM',
  display: 'Emergency - An unforeseen combination of circumstances or the resulting state that calls for immediate action',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_PreopUsedToIndicateThatAServiceIsToBePerformedPriorToAScheduledSurgeryWhenOrderingAServiceAndUsingThePreOpPriorityACheckIsDoneToSeeTheAmountOfTimeThatMustBeAllowedForPerformanceOfTheServiceWhenTheOrder: Coding = {
  code: 'P',
  display:
    'Preop - Used to indicate that a service is to be performed prior to a scheduled surgery.  When ordering a service and using the pre-op priority, a check is done to see the amount of time that must be allowed for performance of the service.  When the order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_AsNeededAnQuotationAsNeededQuotationOrderShouldBeAccompaniedByADescriptionOfWhatConstitutesANeedThisDescriptionIsRepresentedByAnObservationServicePredicateAsAPrecondition: Coding = {
  code: 'PRN',
  display:
    'As needed - An "as needed" order should be accompanied by a description of what constitutes a need.  This description is represented by an observation service predicate as a precondition.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_RoutineRoutineServiceDoAtUsualWorkHours: Coding = {
  code: 'R',
  display: 'Routine - Routine service, do at usual work hours',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_RushReportingAReportShouldBePreparedAndSentAsQuicklyAsPossible: Coding = {
  code: 'RR',
  display: 'Rush reporting - A report should be prepared and sent as quickly as possible',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_StatWithHighestPriorityEGEmergency: Coding = {
  code: 'S',
  display: 'Stat - With highest priority (e.g. emergency).',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_TimingCriticalItIsCriticalToComeAsCloseAsPossibleToTheRequestedTimeEGForAThroughAntimicrobialLevel: Coding = {
  code: 'T',
  display: 'Timing critical - It is critical to come as close as possible to the requested time (e.g. for a through antimicrobial level).',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_UseAsDirectedDrugIsToBeUsedAsDirectedByThePrescriber: Coding = {
  code: 'UD',
  display: 'Use as directed - Drug is to be used as directed by the prescriber.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
const v2_0906_UrgentCallsForPromptAction: Coding = {
  code: 'UR',
  display: 'Urgent - Calls for prompt action',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0906',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0906 ( ActPriority)
 */
export const V20906 = {
  ASAPAsSoonAsPossibleNextHighestPriorityAfterStat: v2_0906_ASAPAsSoonAsPossibleNextHighestPriorityAfterStat,
  CallbackResultsFillerShouldContactThePlacerAsSoonAsResultsAreAvailableEvenForPreliminaryResults: v2_0906_CallbackResultsFillerShouldContactThePlacerAsSoonAsResultsAreAvailableEvenForPreliminaryResults,
  CallbackForSchedulingFillerShouldContactThePlacerOrTargetToScheduleTheService: v2_0906_CallbackForSchedulingFillerShouldContactThePlacerOrTargetToScheduleTheService,
  CallbackPlacerForSchedulingFillerShouldContactThePlacerToScheduleTheService: v2_0906_CallbackPlacerForSchedulingFillerShouldContactThePlacerToScheduleTheService,
  ContactRecipientForSchedulingFillerShouldContactTheServiceRecipientTargetToScheduleTheService: v2_0906_ContactRecipientForSchedulingFillerShouldContactTheServiceRecipientTargetToScheduleTheService,
  ElectiveBeneficialToThePatientButNotEssentialForSurvival: v2_0906_ElectiveBeneficialToThePatientButNotEssentialForSurvival,
  EmergencyAnUnforeseenCombinationOfCircumstancesOrTheResultingStateThatCallsForImmediateAction: v2_0906_EmergencyAnUnforeseenCombinationOfCircumstancesOrTheResultingStateThatCallsForImmediateAction,
  PreopUsedToIndicateThatAServiceIsToBePerformedPriorToAScheduledSurgeryWhenOrderingAServiceAndUsingThePreOpPriorityACheckIsDoneToSeeTheAmountOfTimeThatMustBeAllowedForPerformanceOfTheServiceWhenTheOrder: v2_0906_PreopUsedToIndicateThatAServiceIsToBePerformedPriorToAScheduledSurgeryWhenOrderingAServiceAndUsingThePreOpPriorityACheckIsDoneToSeeTheAmountOfTimeThatMustBeAllowedForPerformanceOfTheServiceWhenTheOrder,
  AsNeededAnQuotationAsNeededQuotationOrderShouldBeAccompaniedByADescriptionOfWhatConstitutesANeedThisDescriptionIsRepresentedByAnObservationServicePredicateAsAPrecondition: v2_0906_AsNeededAnQuotationAsNeededQuotationOrderShouldBeAccompaniedByADescriptionOfWhatConstitutesANeedThisDescriptionIsRepresentedByAnObservationServicePredicateAsAPrecondition,
  RoutineRoutineServiceDoAtUsualWorkHours: v2_0906_RoutineRoutineServiceDoAtUsualWorkHours,
  RushReportingAReportShouldBePreparedAndSentAsQuicklyAsPossible: v2_0906_RushReportingAReportShouldBePreparedAndSentAsQuicklyAsPossible,
  StatWithHighestPriorityEGEmergency: v2_0906_StatWithHighestPriorityEGEmergency,
  TimingCriticalItIsCriticalToComeAsCloseAsPossibleToTheRequestedTimeEGForAThroughAntimicrobialLevel: v2_0906_TimingCriticalItIsCriticalToComeAsCloseAsPossibleToTheRequestedTimeEGForAThroughAntimicrobialLevel,
  UseAsDirectedDrugIsToBeUsedAsDirectedByThePrescriber: v2_0906_UseAsDirectedDrugIsToBeUsedAsDirectedByThePrescriber,
  UrgentCallsForPromptAction: v2_0906_UrgentCallsForPromptAction,
};
const v2_0907_BusinessSinceTheServiceClassCanRepresentKnowledgeStructuresThatMayBeConsideredATradeOrBusinessSecretThereIsSometimesThoughRarelyTheNeedToFlagThoseItemsAsOfBusinessLevelConfidentialityHoweverNoPatientRelatedInf: Coding = {
  code: 'B',
  display:
    'Business - Since the service class can represent knowledge structures that may be considered a trade or business secret, there is sometimes (though rarely) the need to flag those items as of business level confidentiality.  However, no patient related inf',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
const v2_0907_CelebrityCelebritiesArePeopleOfPublicInterestVIPIncludingEmployeesWhoseInformationRequireSpecialProtection: Coding = {
  code: 'C',
  display: 'Celebrity - Celebrities are people of public interest (VIP) including employees, whose information require special protection.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
const v2_0907_ClinicianOnlyCliniciansMaySeeThisItemBillingAndAdministrationPersonsCanNotAccessThisItemWithoutSpecialPermission: Coding = {
  code: 'D',
  display:
    'Clinician - Only clinicians may see this item, billing and administration persons can not access this item without special permission.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
const v2_0907_SubstanceAbuseRelatedAlcoholDrugAbuseRelatedItem: Coding = {
  code: 'ETH',
  display: 'Substance abuse related - Alcohol/drug-abuse related item',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
const v2_0907_HIVRelatedHIVAndAIDSRelatedItem: Coding = {
  code: 'HIV',
  display: 'HIV Related - HIV and AIDS related item',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
const v2_0907_IndividualAccessOnlyToIndividualPersonsWhoAreMentionedExplicitlyAsActorsOfThisServiceAndWhoseActorTypeWarrantsThatAccessCfToActorTypedCode: Coding = {
  code: 'I',
  display:
    'Individual - Access only to individual persons who are mentioned explicitly as actors of this service and whose actor type warrants that access (cf. to actor typed code).',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
const v2_0907_LowNoPatientRecordItemCanBeOfLowConfidentialityHoweverSomeServiceObjectsAreNotPatientRelatedAndThereforeMayHaveLowConfidentiality: Coding = {
  code: 'L',
  display:
    'Low - No patient record item can be of low confidentiality.  However, some service objects are not patient related and therefore may have low confidentiality.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
const v2_0907_NormalNormalConfidentialityRulesAccordingToGoodHealthCarePracticeApplyThatIsOnlyAuthorizedIndividualsWithALegitimateMedicalOrBusinessNeedMayAccessThisItem: Coding = {
  code: 'N',
  display:
    'Normal - Normal confidentiality rules (according to good health care practice) apply, that is, only authorized individuals with a legitimate medical or business need may access this item.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
const v2_0907_PsychiatryRelatedPsychiatryRelatedItem: Coding = {
  code: 'PSY',
  display: 'Psychiatry related - Psychiatry related item',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
const v2_0907_RestrictedRestrictedAccessEGOnlyToProvidersHavingACurrentCareRelationshipToThePatient: Coding = {
  code: 'R',
  display: 'Restricted - Restricted access, e.g. only to providers having a current care relationship to the patient.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
const v2_0907_SensitiveInformationForWhichThePatientSeeksHeightenedConfidentialitySensitiveInformationIsNotToBeSharedWithFamilyMembersInformationReportedByThePatientAboutFamilyMembersIsSensitiveByDefaultFlagCanBeSetOrCleared: Coding = {
  code: 'S',
  display:
    'Sensitive - Information for which the patient seeks heightened confidentiality.  Sensitive information is not to be shared with family members.  Information reported by the patient about family members is sensitive by default.  Flag can be set or cleared ',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
const v2_0907_SexualAndDomesticViolenceRelatedSexualAssaultDomesticViolenceRelatedItem: Coding = {
  code: 'SDV',
  display: 'Sexual and domestic violence related - Sexual assault / domestic violence related item',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
const v2_0907_TabooInformationNotToBeDisclosedOrDiscussedWithPatientExceptThroughPhysicianAssignedToPatientInThisCaseThisIsUsuallyATemporaryConstraintOnlyExampleUseIsANewFatalDiagnosisOrFindingSuchAsMalignancyOrHIV: Coding = {
  code: 'T',
  display:
    'Taboo - Information not to be disclosed or discussed with patient except through physician assigned to patient in this case.  This is usually a temporary constraint only; example use is a new fatal diagnosis or finding, such as malignancy or HIV.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
const v2_0907_VeryRestrictedVeryRestrictedAccessAsDeclaredByThePrivacyOfficerOfTheRecordHolder: Coding = {
  code: 'V',
  display: 'Very restricted - Very restricted access as declared by the Privacy Officer of the record holder.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0907',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0907 ( Confidentiality)
 */
export const V20907 = {
  BusinessSinceTheServiceClassCanRepresentKnowledgeStructuresThatMayBeConsideredATradeOrBusinessSecretThereIsSometimesThoughRarelyTheNeedToFlagThoseItemsAsOfBusinessLevelConfidentialityHoweverNoPatientRelatedInf: v2_0907_BusinessSinceTheServiceClassCanRepresentKnowledgeStructuresThatMayBeConsideredATradeOrBusinessSecretThereIsSometimesThoughRarelyTheNeedToFlagThoseItemsAsOfBusinessLevelConfidentialityHoweverNoPatientRelatedInf,
  CelebrityCelebritiesArePeopleOfPublicInterestVIPIncludingEmployeesWhoseInformationRequireSpecialProtection: v2_0907_CelebrityCelebritiesArePeopleOfPublicInterestVIPIncludingEmployeesWhoseInformationRequireSpecialProtection,
  ClinicianOnlyCliniciansMaySeeThisItemBillingAndAdministrationPersonsCanNotAccessThisItemWithoutSpecialPermission: v2_0907_ClinicianOnlyCliniciansMaySeeThisItemBillingAndAdministrationPersonsCanNotAccessThisItemWithoutSpecialPermission,
  SubstanceAbuseRelatedAlcoholDrugAbuseRelatedItem: v2_0907_SubstanceAbuseRelatedAlcoholDrugAbuseRelatedItem,
  HIVRelatedHIVAndAIDSRelatedItem: v2_0907_HIVRelatedHIVAndAIDSRelatedItem,
  IndividualAccessOnlyToIndividualPersonsWhoAreMentionedExplicitlyAsActorsOfThisServiceAndWhoseActorTypeWarrantsThatAccessCfToActorTypedCode: v2_0907_IndividualAccessOnlyToIndividualPersonsWhoAreMentionedExplicitlyAsActorsOfThisServiceAndWhoseActorTypeWarrantsThatAccessCfToActorTypedCode,
  LowNoPatientRecordItemCanBeOfLowConfidentialityHoweverSomeServiceObjectsAreNotPatientRelatedAndThereforeMayHaveLowConfidentiality: v2_0907_LowNoPatientRecordItemCanBeOfLowConfidentialityHoweverSomeServiceObjectsAreNotPatientRelatedAndThereforeMayHaveLowConfidentiality,
  NormalNormalConfidentialityRulesAccordingToGoodHealthCarePracticeApplyThatIsOnlyAuthorizedIndividualsWithALegitimateMedicalOrBusinessNeedMayAccessThisItem: v2_0907_NormalNormalConfidentialityRulesAccordingToGoodHealthCarePracticeApplyThatIsOnlyAuthorizedIndividualsWithALegitimateMedicalOrBusinessNeedMayAccessThisItem,
  PsychiatryRelatedPsychiatryRelatedItem: v2_0907_PsychiatryRelatedPsychiatryRelatedItem,
  RestrictedRestrictedAccessEGOnlyToProvidersHavingACurrentCareRelationshipToThePatient: v2_0907_RestrictedRestrictedAccessEGOnlyToProvidersHavingACurrentCareRelationshipToThePatient,
  SensitiveInformationForWhichThePatientSeeksHeightenedConfidentialitySensitiveInformationIsNotToBeSharedWithFamilyMembersInformationReportedByThePatientAboutFamilyMembersIsSensitiveByDefaultFlagCanBeSetOrCleared: v2_0907_SensitiveInformationForWhichThePatientSeeksHeightenedConfidentialitySensitiveInformationIsNotToBeSharedWithFamilyMembersInformationReportedByThePatientAboutFamilyMembersIsSensitiveByDefaultFlagCanBeSetOrCleared,
  SexualAndDomesticViolenceRelatedSexualAssaultDomesticViolenceRelatedItem: v2_0907_SexualAndDomesticViolenceRelatedSexualAssaultDomesticViolenceRelatedItem,
  TabooInformationNotToBeDisclosedOrDiscussedWithPatientExceptThroughPhysicianAssignedToPatientInThisCaseThisIsUsuallyATemporaryConstraintOnlyExampleUseIsANewFatalDiagnosisOrFindingSuchAsMalignancyOrHIV: v2_0907_TabooInformationNotToBeDisclosedOrDiscussedWithPatientExceptThroughPhysicianAssignedToPatientInThisCaseThisIsUsuallyATemporaryConstraintOnlyExampleUseIsANewFatalDiagnosisOrFindingSuchAsMalignancyOrHIV,
  VeryRestrictedVeryRestrictedAccessAsDeclaredByThePrivacyOfficerOfTheRecordHolder: v2_0907_VeryRestrictedVeryRestrictedAccessAsDeclaredByThePrivacyOfficerOfTheRecordHolder,
};
const v2_0909_ShareIn1DayLessThanPGreaterThanShareResultRegardlessOfReferenceTherapeuticRangeAfter1OrMoreBusinessDayAsAgreedToByTheSystemsInPlay: Coding = {
  code: 'SID',
  display:
    'Share In1 Day -<p>Share result regardless of reference/therapeutic range after 1 or more business day as agreed to by the systems in play.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0909',
};
const v2_0909_ShareIn1DayConditionallyLessThanPGreaterThanShareResultInReferenceRangesTherapeuticWithPatientAfter1OrMoreBusinessDayAsAgreedToByTheSystemsInPlayLessThanPGreaterThanWithholdResultOutOfReferenceTherapeuticRangeUntilPhysicianRelease: Coding = {
  code: 'SIDC',
  display:
    'Share in 1 Day Conditionally -<p>Share result in reference ranges/therapeutic with patient after 1 or more business day as agreed to by the systems in play.<p>Withhold result out of reference/therapeutic range until physician release',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0909',
};
const v2_0909_ShareImmediatelyLessThanPGreaterThanShareResultWithPatientImmediately: Coding = {
  code: 'SIMM',
  display: 'Share Immediately -<p>Share result with patient immediately',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0909',
};
const v2_0909_ShareToBeDeterminedLessThanPGreaterThanCategoryToBeDetermined: Coding = {
  code: 'STBD',
  display: 'Share To Be Determined -<p>Category to be determined',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0909',
};
const v2_0909_ShareWithinNormalLimitsLessThanPGreaterThanShareResultInReferenceTherapeuticRangeWithPatientImmediatelyLessThanPGreaterThanShareResultOutOfReferenceTherapeuticRangesWithPatientAfter1OrMoreBusinessDayAsAgreedToByTheSystemsInPlay: Coding = {
  code: 'SWNL',
  display:
    'Share Within Normal Limits -<p>Share result in reference/therapeutic range with patient immediately<p>Share result out of reference/therapeutic ranges with patient after 1 or more business day as agreed to by the systems in play.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0909',
};
const v2_0909_ShareWithholdLessThanPGreaterThanWithholdResultRegardlessOfReferenceTherapeuticRanges: Coding = {
  code: 'SWTH',
  display: 'Share Withhold -<p>Withhold result regardless of reference/therapeutic ranges',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0909',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0909 ( Patient Results Release Categorization Scheme)
 */
export const V20909 = {
  ShareIn1DayLessThanPGreaterThanShareResultRegardlessOfReferenceTherapeuticRangeAfter1OrMoreBusinessDayAsAgreedToByTheSystemsInPlay: v2_0909_ShareIn1DayLessThanPGreaterThanShareResultRegardlessOfReferenceTherapeuticRangeAfter1OrMoreBusinessDayAsAgreedToByTheSystemsInPlay,
  ShareIn1DayConditionallyLessThanPGreaterThanShareResultInReferenceRangesTherapeuticWithPatientAfter1OrMoreBusinessDayAsAgreedToByTheSystemsInPlayLessThanPGreaterThanWithholdResultOutOfReferenceTherapeuticRangeUntilPhysicianRelease: v2_0909_ShareIn1DayConditionallyLessThanPGreaterThanShareResultInReferenceRangesTherapeuticWithPatientAfter1OrMoreBusinessDayAsAgreedToByTheSystemsInPlayLessThanPGreaterThanWithholdResultOutOfReferenceTherapeuticRangeUntilPhysicianRelease,
  ShareImmediatelyLessThanPGreaterThanShareResultWithPatientImmediately: v2_0909_ShareImmediatelyLessThanPGreaterThanShareResultWithPatientImmediately,
  ShareToBeDeterminedLessThanPGreaterThanCategoryToBeDetermined: v2_0909_ShareToBeDeterminedLessThanPGreaterThanCategoryToBeDetermined,
  ShareWithinNormalLimitsLessThanPGreaterThanShareResultInReferenceTherapeuticRangeWithPatientImmediatelyLessThanPGreaterThanShareResultOutOfReferenceTherapeuticRangesWithPatientAfter1OrMoreBusinessDayAsAgreedToByTheSystemsInPlay: v2_0909_ShareWithinNormalLimitsLessThanPGreaterThanShareResultInReferenceTherapeuticRangeWithPatientImmediatelyLessThanPGreaterThanShareResultOutOfReferenceTherapeuticRangesWithPatientAfter1OrMoreBusinessDayAsAgreedToByTheSystemsInPlay,
  ShareWithholdLessThanPGreaterThanWithholdResultRegardlessOfReferenceTherapeuticRanges: v2_0909_ShareWithholdLessThanPGreaterThanWithholdResultRegardlessOfReferenceTherapeuticRanges,
};
const v2_0912_AlertAcknowledgingProvider: Coding = {
  code: 'AAP',
  display: 'Alert Acknowledging Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_AdministrationCosigner: Coding = {
  code: 'AC',
  display: 'Administration Cosigner',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_AdmittingProvider: Coding = {
  code: 'AD',
  display: 'Admitting Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_AuthorizedPerformingHealthProfessional: Coding = {
  code: 'AHP',
  display: 'Authorized Performing Health Professional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_AssistantAlternateInterpreter: Coding = {
  code: 'AI',
  display: 'Assistant/Alternate Interpreter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_AdministeringProvider: Coding = {
  code: 'AP',
  display: 'Administering Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_AuthorizedPerformingProviderOrganization: Coding = {
  code: 'APO',
  display: 'Authorized Performing Provider Organization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_AssistantResultInterpreter: Coding = {
  code: 'ARI',
  display: 'Assistant Result Interpreter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_AttendingProvider: Coding = {
  code: 'AT',
  display: 'Attending Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_AuthorEventInitiator: Coding = {
  code: 'AUT',
  display: 'Author/Event Initiator',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_ConsultingProvider: Coding = {
  code: 'CP',
  display: 'Consulting Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_DispensingProvider: Coding = {
  code: 'DP',
  display: 'Dispensing Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_ExpectedAssistantResultInterpreter: Coding = {
  code: 'EARI',
  display: 'Expected Assistant Result Interpreter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_EnteringPerson: Coding = {
  code: 'EP',
  display: 'Entering Person',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_ExpectedPrincipalResultInterpreter: Coding = {
  code: 'EPRI',
  display: 'Expected Principal Result Interpreter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_Equipment: Coding = {
  code: 'EQUIP',
  display: 'Equipment',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_FamilyHealthCareProfessional: Coding = {
  code: 'FHCP',
  display: 'Family Health Care Professional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_MedicalDirector: Coding = {
  code: 'MDIR',
  display: 'Medical Director',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_OrderingProvider: Coding = {
  code: 'OP',
  display: 'Ordering Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_OutsideSiteSWhereObservationMayBePerformed: Coding = {
  code: 'OS',
  display: 'Outside Site(s) Where Observation May Be Performed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_PackedBy: Coding = {
  code: 'PB',
  display: 'Packed by',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_Pharmacist: Coding = {
  code: 'PH',
  display: 'Pharmacist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_PrimaryInterpreter: Coding = {
  code: 'PI',
  display: 'Primary Interpreter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_PerformingOrganization: Coding = {
  code: 'PO',
  display: 'Performing Organization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_PerformingOrganizationMedicalDirector: Coding = {
  code: 'POMD',
  display: 'Performing Organization Medical Director',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_PrimaryCareProvider: Coding = {
  code: 'PP',
  display: 'Primary Care Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_PatientPreferredPharmacy: Coding = {
  code: 'PPH',
  display: 'Patient Preferred Pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_PrincipalResultInterpreter: Coding = {
  code: 'PRI',
  display: 'Principal Result Interpreter',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_ResultsCopiesTo: Coding = {
  code: 'RCT',
  display: 'Results Copies To',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_ReferringHealthProfessional: Coding = {
  code: 'RHP',
  display: 'Referring Health Professional',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_ResponsibleObserver: Coding = {
  code: 'RO',
  display: 'Responsible Observer',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_ReferringProvider: Coding = {
  code: 'RP',
  display: 'Referring Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_ReferringProviderOrganization: Coding = {
  code: 'RPO',
  display: 'Referring Provider Organization',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_ReferredToProvider: Coding = {
  code: 'RT',
  display: 'Referred to Provider',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_SendBy: Coding = {
  code: 'SB',
  display: 'Send by',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_SpecimenCollector: Coding = {
  code: 'SC',
  display: 'Specimen Collector',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_Technician: Coding = {
  code: 'TN',
  display: 'Technician',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_Transcriptionist: Coding = {
  code: 'TR',
  display: 'Transcriptionist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_VerifierVerifyingPerson: Coding = {
  code: 'VP',
  display: 'Verifier (Verifying Person)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_VerifyingPharmaceuticalSupplierNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID: Coding = {
  code: 'VPS',
  display: "Verifying Pharmaceutical Supplier   (not sure how to dissect Pharmacist/Treatment Supplier's Verifier ID)",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_VerifyingTreatmentSupplierNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID: Coding = {
  code: 'VTS',
  display: "Verifying Treatment Supplier   (not sure how to dissect Pharmacist/Treatment Supplier's Verifier ID)",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_Waypoint: Coding = {
  code: 'WAY',
  display: 'Waypoint',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
const v2_0912_WaypointRecipient: Coding = {
  code: 'WAYR',
  display: 'Waypoint Recipient',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0912',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0912 ( Participation)
 */
export const V20912 = {
  AlertAcknowledgingProvider: v2_0912_AlertAcknowledgingProvider,
  AdministrationCosigner: v2_0912_AdministrationCosigner,
  AdmittingProvider: v2_0912_AdmittingProvider,
  AuthorizedPerformingHealthProfessional: v2_0912_AuthorizedPerformingHealthProfessional,
  AssistantAlternateInterpreter: v2_0912_AssistantAlternateInterpreter,
  AdministeringProvider: v2_0912_AdministeringProvider,
  AuthorizedPerformingProviderOrganization: v2_0912_AuthorizedPerformingProviderOrganization,
  AssistantResultInterpreter: v2_0912_AssistantResultInterpreter,
  AttendingProvider: v2_0912_AttendingProvider,
  AuthorEventInitiator: v2_0912_AuthorEventInitiator,
  ConsultingProvider: v2_0912_ConsultingProvider,
  DispensingProvider: v2_0912_DispensingProvider,
  ExpectedAssistantResultInterpreter: v2_0912_ExpectedAssistantResultInterpreter,
  EnteringPerson: v2_0912_EnteringPerson,
  ExpectedPrincipalResultInterpreter: v2_0912_ExpectedPrincipalResultInterpreter,
  Equipment: v2_0912_Equipment,
  FamilyHealthCareProfessional: v2_0912_FamilyHealthCareProfessional,
  MedicalDirector: v2_0912_MedicalDirector,
  OrderingProvider: v2_0912_OrderingProvider,
  OutsideSiteSWhereObservationMayBePerformed: v2_0912_OutsideSiteSWhereObservationMayBePerformed,
  PackedBy: v2_0912_PackedBy,
  Pharmacist: v2_0912_Pharmacist,
  PrimaryInterpreter: v2_0912_PrimaryInterpreter,
  PerformingOrganization: v2_0912_PerformingOrganization,
  PerformingOrganizationMedicalDirector: v2_0912_PerformingOrganizationMedicalDirector,
  PrimaryCareProvider: v2_0912_PrimaryCareProvider,
  PatientPreferredPharmacy: v2_0912_PatientPreferredPharmacy,
  PrincipalResultInterpreter: v2_0912_PrincipalResultInterpreter,
  ResultsCopiesTo: v2_0912_ResultsCopiesTo,
  ReferringHealthProfessional: v2_0912_ReferringHealthProfessional,
  ResponsibleObserver: v2_0912_ResponsibleObserver,
  ReferringProvider: v2_0912_ReferringProvider,
  ReferringProviderOrganization: v2_0912_ReferringProviderOrganization,
  ReferredToProvider: v2_0912_ReferredToProvider,
  SendBy: v2_0912_SendBy,
  SpecimenCollector: v2_0912_SpecimenCollector,
  Technician: v2_0912_Technician,
  Transcriptionist: v2_0912_Transcriptionist,
  VerifierVerifyingPerson: v2_0912_VerifierVerifyingPerson,
  VerifyingPharmaceuticalSupplierNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID: v2_0912_VerifyingPharmaceuticalSupplierNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID,
  VerifyingTreatmentSupplierNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID: v2_0912_VerifyingTreatmentSupplierNotSureHowToDissectPharmacistTreatmentSupplierQuoteSVerifierID,
  Waypoint: v2_0912_Waypoint,
  WaypointRecipient: v2_0912_WaypointRecipient,
};
const v2_0914_AnalysisProcess: Coding = {
  code: 'AP',
  display: 'Analysis Process',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0914',
};
const v2_0914_InformationManagement: Coding = {
  code: 'IM',
  display: 'Information Management',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0914',
};
const v2_0914_Laboratory: Coding = {
  code: 'L',
  display: 'Laboratory',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0914',
};
const v2_0914_NotApplicable: Coding = {
  code: 'NA',
  display: 'Not Applicable',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0914',
};
const v2_0914_PlacerData: Coding = {
  code: 'PD',
  display: 'Placer Data',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0914',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0914 ( Root Cause)
 */
export const V20914 = {
  AnalysisProcess: v2_0914_AnalysisProcess,
  InformationManagement: v2_0914_InformationManagement,
  Laboratory: v2_0914_Laboratory,
  NotApplicable: v2_0914_NotApplicable,
  PlacerData: v2_0914_PlacerData,
};
const v2_0916_PatientWasFastingPriorToTheProcedure: Coding = {
  code: 'F',
  display: 'Patient was fasting prior to the procedure.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0916',
};
const v2_0916_ThePatientIndicatedTheyDidNotFastPriorToTheProcedure: Coding = {
  code: 'NF',
  display: 'The patient indicated they did not fast prior to the procedure.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0916',
};
const v2_0916_NotGivenPatientWasNotAskedAtTheTimeOfTheProcedure: Coding = {
  code: 'NG',
  display: 'Not Given - Patient was not asked at the time of the procedure.',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0916',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0916 ( Relevant Clincial Information)
 */
export const V20916 = {
  PatientWasFastingPriorToTheProcedure: v2_0916_PatientWasFastingPriorToTheProcedure,
  ThePatientIndicatedTheyDidNotFastPriorToTheProcedure: v2_0916_ThePatientIndicatedTheyDidNotFastPriorToTheProcedure,
  NotGivenPatientWasNotAskedAtTheTimeOfTheProcedure: v2_0916_NotGivenPatientWasNotAskedAtTheTimeOfTheProcedure,
};
const v2_0917_Supplemental: Coding = {
  code: 'C',
  display: 'Supplemental',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0917',
};
const v2_0917_Loading: Coding = {
  code: 'L',
  display: 'Loading',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0917',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0917 ( Bolus Type)
 */
export const V20917 = {
  Supplemental: v2_0917_Supplemental,
  Loading: v2_0917_Loading,
};
const v2_0918_Continuous: Coding = {
  code: 'C',
  display: 'Continuous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0918',
};
const v2_0918_PCAOnly: Coding = {
  code: 'P',
  display: 'PCA Only',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0918',
};
const v2_0918_PCAPlusContinuous: Coding = {
  code: 'PC',
  display: 'PCA + Continuous',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0918',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0918 ( PCA Type)
 */
export const V20918 = {
  Continuous: v2_0918_Continuous,
  PCAOnly: v2_0918_PCAOnly,
  PCAPlusContinuous: v2_0918_PCAPlusContinuous,
};
const v2_0919_InSomeCasesThisTestShouldBeOnlyExclusivelyWithLikeTestsExamplesAreCytoOrPathology: Coding = {
  code: 'D',
  display: 'In some cases, this test should be only exclusively with like tests (examples are cyto or pathology)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0919',
};
const v2_0919_ThisTestCanBeIncludedWithAnyNumberOfOtherTests: Coding = {
  code: 'N',
  display: 'This test can be included with any number of other tests',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0919',
};
const v2_0919_ThisTestShouldBeExclusive: Coding = {
  code: 'Y',
  display: 'This test should be exclusive',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0919',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0919 ( Exclusive Test)
 */
export const V20919 = {
  InSomeCasesThisTestShouldBeOnlyExclusivelyWithLikeTestsExamplesAreCytoOrPathology: v2_0919_InSomeCasesThisTestShouldBeOnlyExclusivelyWithLikeTestsExamplesAreCytoOrPathology,
  ThisTestCanBeIncludedWithAnyNumberOfOtherTests: v2_0919_ThisTestCanBeIncludedWithAnyNumberOfOtherTests,
  ThisTestShouldBeExclusive: v2_0919_ThisTestShouldBeExclusive,
};
const v2_0920_Alternate: Coding = {
  code: 'A',
  display: 'Alternate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0920',
};
const v2_0920_Preferred: Coding = {
  code: 'P',
  display: 'Preferred',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0920',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0920 ( Preferred Specimen/Attribute Status)
 */
export const V20920 = {
  Alternate: v2_0920_Alternate,
  Preferred: v2_0920_Preferred,
};
const v2_0921_Admitting: Coding = {
  code: 'ADM',
  display: 'Admitting',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0921',
};
const v2_0921_Procedure: Coding = {
  code: 'PROC',
  display: 'Procedure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0921',
};
const v2_0921_Service: Coding = {
  code: 'SERV',
  display: 'Service',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0921',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0921 ( Certification Type Code)
 */
export const V20921 = {
  Admitting: v2_0921_Admitting,
  Procedure: v2_0921_Procedure,
  Service: v2_0921_Service,
};
const v2_0922_InitialRequest: Coding = {
  code: 'IR',
  display: 'Initial Request',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0922',
};
const v2_0922_RequestForAppeal: Coding = {
  code: 'RA',
  display: 'Request for Appeal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0922',
};
const v2_0922_RequestForExtension: Coding = {
  code: 'RE',
  display: 'Request for Extension',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0922',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0922 ( Certification Category Code)
 */
export const V20922 = {
  InitialRequest: v2_0922_InitialRequest,
  RequestForAppeal: v2_0922_RequestForAppeal,
  RequestForExtension: v2_0922_RequestForExtension,
};
const v2_0923_AbortedRunProcessInterruptedAfterThePhlebotomistInsertsTheNeedleInTheDonorQuoteSArm: Coding = {
  code: 'ABR',
  display: "Aborted Run: Process interrupted after the Phlebotomist inserts the needle in the Donor's arm",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0923',
};
const v2_0923_ProcessWasNotInterrupted: Coding = {
  code: 'NIN',
  display: 'Process was not interrupted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0923',
};
const v2_0923_WalkOutProcessInterruptedBeforeThePhlebotomistInsertsTheNeedleInTheDonorQuoteSArm: Coding = {
  code: 'WOT',
  display: "Walk Out: Process interrupted before the Phlebotomist inserts the needle in the Donor's arm",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0923',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0923 ( Process Interruption)
 */
export const V20923 = {
  AbortedRunProcessInterruptedAfterThePhlebotomistInsertsTheNeedleInTheDonorQuoteSArm: v2_0923_AbortedRunProcessInterruptedAfterThePhlebotomistInsertsTheNeedleInTheDonorQuoteSArm,
  ProcessWasNotInterrupted: v2_0923_ProcessWasNotInterrupted,
  WalkOutProcessInterruptedBeforeThePhlebotomistInsertsTheNeedleInTheDonorQuoteSArm: v2_0923_WalkOutProcessInterruptedBeforeThePhlebotomistInsertsTheNeedleInTheDonorQuoteSArm,
};
const v2_0924_Annual: Coding = {
  code: 'A',
  display: 'Annual',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0924',
};
const v2_0924_PerDay: Coding = {
  code: 'D',
  display: 'Per Day',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0924',
};
const v2_0924_PerMonth: Coding = {
  code: 'M',
  display: 'Per Month',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0924',
};
const v2_0924_DurationOfTheOrder: Coding = {
  code: 'O',
  display: 'Duration of the Order',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0924',
};
const v2_0924_PatientsLifetime: Coding = {
  code: 'PL',
  display: 'Patients Lifetime',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0924',
};
const v2_0924_PerWeek: Coding = {
  code: 'WK',
  display: 'Per Week',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0924',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0924 ( Cumulative Dosage Limit UoM)
 */
export const V20924 = {
  Annual: v2_0924_Annual,
  PerDay: v2_0924_PerDay,
  PerMonth: v2_0924_PerMonth,
  DurationOfTheOrder: v2_0924_DurationOfTheOrder,
  PatientsLifetime: v2_0924_PatientsLifetime,
  PerWeek: v2_0924_PerWeek,
};
const v2_0925_AirContamination: Coding = {
  code: 'ACN',
  display: 'Air Contamination',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0925',
};
const v2_0925_Clotted: Coding = {
  code: 'CLT',
  display: 'Clotted',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0925',
};
const v2_0925_Collapse: Coding = {
  code: 'COL',
  display: 'Collapse',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0925',
};
const v2_0925_DefectiveApheresisKit: Coding = {
  code: 'DAK',
  display: 'Defective Apheresis Kit',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0925',
};
const v2_0925_DefectiveBag: Coding = {
  code: 'DBG',
  display: 'Defective Bag',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0925',
};
const v2_0925_DefectiveInstrument: Coding = {
  code: 'DMT',
  display: 'Defective Instrument',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0925',
};
const v2_0925_DefectiveNeedle: Coding = {
  code: 'DND',
  display: 'Defective Needle',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0925',
};
const v2_0925_Infiltration: Coding = {
  code: 'INF',
  display: 'Infiltration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0925',
};
const v2_0925_InstrumentPowerFailure: Coding = {
  code: 'IPF',
  display: 'Instrument Power Failure',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0925',
};
const v2_0925_MissedInTissue: Coding = {
  code: 'MIS',
  display: 'Missed / in tissue',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0925',
};
const v2_0925_NeedleAdjustmentThisMayNotEndAProcedureIfSuccessfulWillImpactComponentProduction: Coding = {
  code: 'NAD',
  display: 'Needle adjustment (this may not end a procedure, if successful will impact component production)',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0925',
};
const v2_0925_PoorFlow: Coding = {
  code: 'PFL',
  display: 'Poor flow',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0925',
};
const v2_0925_VeinSpasm: Coding = {
  code: 'VSM',
  display: 'Vein Spasm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0925',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0925 ( Phlebotomy Issue)
 */
export const V20925 = {
  AirContamination: v2_0925_AirContamination,
  Clotted: v2_0925_Clotted,
  Collapse: v2_0925_Collapse,
  DefectiveApheresisKit: v2_0925_DefectiveApheresisKit,
  DefectiveBag: v2_0925_DefectiveBag,
  DefectiveInstrument: v2_0925_DefectiveInstrument,
  DefectiveNeedle: v2_0925_DefectiveNeedle,
  Infiltration: v2_0925_Infiltration,
  InstrumentPowerFailure: v2_0925_InstrumentPowerFailure,
  MissedInTissue: v2_0925_MissedInTissue,
  NeedleAdjustmentThisMayNotEndAProcedureIfSuccessfulWillImpactComponentProduction: v2_0925_NeedleAdjustmentThisMayNotEndAProcedureIfSuccessfulWillImpactComponentProduction,
  PoorFlow: v2_0925_PoorFlow,
  VeinSpasm: v2_0925_VeinSpasm,
};
const v2_0926_NotDrawn: Coding = {
  code: 'NDR',
  display: 'Not Drawn',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0926',
};
const v2_0926_Successful: Coding = {
  code: 'SUC',
  display: 'Successful',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0926',
};
const v2_0926_UnsuccessfulLessThan50MlDrawn: Coding = {
  code: 'UL5',
  display: 'Unsuccessful Less than 50 ml drawn',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0926',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0926 ( Phlebotomy Status)
 */
export const V20926 = {
  NotDrawn: v2_0926_NotDrawn,
  Successful: v2_0926_Successful,
  UnsuccessfulLessThan50MlDrawn: v2_0926_UnsuccessfulLessThan50MlDrawn,
};
const v2_0927_BothArms: Coding = {
  code: 'B',
  display: 'Both Arms',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0927',
};
const v2_0927_LeftArm: Coding = {
  code: 'L',
  display: 'Left Arm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0927',
};
const v2_0927_RightArm: Coding = {
  code: 'R',
  display: 'Right Arm',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0927',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0927 ( Arm Stick)
 */
export const V20927 = {
  BothArms: v2_0927_BothArms,
  LeftArm: v2_0927_LeftArm,
  RightArm: v2_0927_RightArm,
};
const v2_0933_DoubleRedCells: Coding = {
  code: '2RC',
  display: 'Double Red Cells',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0933',
};
const v2_0933_Granulocytes: Coding = {
  code: 'GRN',
  display: 'Granulocytes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0933',
};
const v2_0933_Hemachromatosis: Coding = {
  code: 'HEM',
  display: 'Hemachromatosis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0933',
};
const v2_0933_HematopoieticProgenitorCells: Coding = {
  code: 'HPC',
  display: 'Hematopoietic Progenitor Cells',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0933',
};
const v2_0933_Lymphocytes: Coding = {
  code: 'LYM',
  display: 'Lymphocytes',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0933',
};
const v2_0933_Plasma: Coding = {
  code: 'PLS',
  display: 'Plasma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0933',
};
const v2_0933_Platelets: Coding = {
  code: 'PLT',
  display: 'Platelets',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0933',
};
const v2_0933_PlateletsAndPlasma: Coding = {
  code: 'PNP',
  display: 'Platelets and Plasma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0933',
};
const v2_0933_PlateletsAndRedCells: Coding = {
  code: 'PNR',
  display: 'Platelets and Red Cells',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0933',
};
const v2_0933_PlateletsPlasmaAndRedCells: Coding = {
  code: 'PPR',
  display: 'Platelets, Plasma, and Red Cells',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0933',
};
const v2_0933_TherapeuticApheresis: Coding = {
  code: 'THA',
  display: 'Therapeutic Apheresis',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0933',
};
const v2_0933_TherapeuticWholeBlood: Coding = {
  code: 'THW',
  display: 'Therapeutic Whole Blood',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0933',
};
const v2_0933_WholeBlood: Coding = {
  code: 'WBL',
  display: 'Whole Blood',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0933',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0933 ( Intended Procedure Type)
 */
export const V20933 = {
  DoubleRedCells: v2_0933_DoubleRedCells,
  Granulocytes: v2_0933_Granulocytes,
  Hemachromatosis: v2_0933_Hemachromatosis,
  HematopoieticProgenitorCells: v2_0933_HematopoieticProgenitorCells,
  Lymphocytes: v2_0933_Lymphocytes,
  Plasma: v2_0933_Plasma,
  Platelets: v2_0933_Platelets,
  PlateletsAndPlasma: v2_0933_PlateletsAndPlasma,
  PlateletsAndRedCells: v2_0933_PlateletsAndRedCells,
  PlateletsPlasmaAndRedCells: v2_0933_PlateletsPlasmaAndRedCells,
  TherapeuticApheresis: v2_0933_TherapeuticApheresis,
  TherapeuticWholeBlood: v2_0933_TherapeuticWholeBlood,
  WholeBlood: v2_0933_WholeBlood,
};
const v2_0935_ApheresisSoftwareCrash: Coding = {
  code: 'ASC',
  display: 'Apheresis Software Crash',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0935',
};
const v2_0935_ManufacturingSoftwareCrash: Coding = {
  code: 'BSC',
  display: 'Manufacturing Software Crash',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0935',
};
const v2_0935_CouldnQuoteTFollowThroughWithDonationScared: Coding = {
  code: 'CFT',
  display: "Couldn't follow through with donation (scared)",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0935',
};
const v2_0935_Bathroom: Coding = {
  code: 'DBB',
  display: 'Bathroom',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0935',
};
const v2_0935_CouldnQuoteTWait: Coding = {
  code: 'DCW',
  display: "Couldn't wait",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0935',
};
const v2_0935_PhlebotomyIssue: Coding = {
  code: 'DNI',
  display: 'Phlebotomy Issue',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0935',
};
const v2_0935_GeneralFacilityEmergency: Coding = {
  code: 'GFE',
  display: 'General Facility Emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0935',
};
const v2_0935_NoReasonGivenDonorDecidedToStopWithoutGivingAReason: Coding = {
  code: 'NRG',
  display: 'No reason given, donor decided to stop without giving a reason',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0935',
};
const v2_0935_PhoneCallDonor: Coding = {
  code: 'PCD',
  display: 'Phone Call-Donor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0935',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0935 ( Process Interruption Reason)
 */
export const V20935 = {
  ApheresisSoftwareCrash: v2_0935_ApheresisSoftwareCrash,
  ManufacturingSoftwareCrash: v2_0935_ManufacturingSoftwareCrash,
  CouldnQuoteTFollowThroughWithDonationScared: v2_0935_CouldnQuoteTFollowThroughWithDonationScared,
  Bathroom: v2_0935_Bathroom,
  CouldnQuoteTWait: v2_0935_CouldnQuoteTWait,
  PhlebotomyIssue: v2_0935_PhlebotomyIssue,
  GeneralFacilityEmergency: v2_0935_GeneralFacilityEmergency,
  NoReasonGivenDonorDecidedToStopWithoutGivingAReason: v2_0935_NoReasonGivenDonorDecidedToStopWithoutGivingAReason,
  PhoneCallDonor: v2_0935_PhoneCallDonor,
};
const v2_2_4_0006_ChristianAmericanBaptistChurch: Coding = {
  code: 'ABC',
  display: 'Christian: American Baptist Church',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Agnostic: Coding = {
  code: 'AGN',
  display: 'Agnostic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianAfricanMethodistEpiscopalZion: Coding = {
  code: 'AME',
  display: 'Christian: African Methodist Episcopal Zion',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianAfricanMethodistEpiscopal: Coding = {
  code: 'AMT',
  display: 'Christian: African Methodist Episcopal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianAnglican: Coding = {
  code: 'ANG',
  display: 'Christian: Anglican',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianAssemblyOfGod: Coding = {
  code: 'AOG',
  display: 'Christian: Assembly of God',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Atheist: Coding = {
  code: 'ATH',
  display: 'Atheist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_BahaQuoteI: Coding = {
  code: 'BAH',
  display: "Baha'i",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianBaptist: Coding = {
  code: 'BAP',
  display: 'Christian: Baptist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_BuddhistMahayana: Coding = {
  code: 'BMA',
  display: 'Buddhist: Mahayana',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_BuddhistOther: Coding = {
  code: 'BOT',
  display: 'Buddhist: Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Brethren: Coding = {
  code: 'BRE',
  display: 'Brethren',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_BuddhistTantrayana: Coding = {
  code: 'BTA',
  display: 'Buddhist: Tantrayana',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_BuddhistTheravada: Coding = {
  code: 'BTH',
  display: 'Buddhist: Theravada',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Buddhist: Coding = {
  code: 'BUD',
  display: 'Buddhist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianRomanCatholic: Coding = {
  code: 'CAT',
  display: 'Christian: Roman Catholic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChineseFolkReligionist: Coding = {
  code: 'CFR',
  display: 'Chinese Folk Religionist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Christian: Coding = {
  code: 'CHR',
  display: 'Christian',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianChristianScience: Coding = {
  code: 'CHS',
  display: 'Christian: Christian Science',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianChristianMissionaryAlliance: Coding = {
  code: 'CMA',
  display: 'Christian: Christian Missionary Alliance',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Confucian: Coding = {
  code: 'CNF',
  display: 'Confucian',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianChurchOfChrist: Coding = {
  code: 'COC',
  display: 'Christian: Church of Christ',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianChurchOfGod: Coding = {
  code: 'COG',
  display: 'Christian: Church of God',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianChurchOfGodInChrist: Coding = {
  code: 'COI',
  display: 'Christian: Church of God in Christ',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianCongregational: Coding = {
  code: 'COL',
  display: 'Christian: Congregational',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianCommunity: Coding = {
  code: 'COM',
  display: 'Christian: Community',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianOtherPentecostal: Coding = {
  code: 'COP',
  display: 'Christian: Other Pentecostal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianOther: Coding = {
  code: 'COT',
  display: 'Christian: Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianChristianReformed: Coding = {
  code: 'CRR',
  display: 'Christian: Christian Reformed',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_DisciplesOfChrist: Coding = {
  code: 'DOC',
  display: 'Disciples of Christ',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianEasternOrthodox: Coding = {
  code: 'EOT',
  display: 'Christian: Eastern Orthodox',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianEpiscopalian: Coding = {
  code: 'EPI',
  display: 'Christian: Episcopalian',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_EthnicReligionist: Coding = {
  code: 'ERL',
  display: 'Ethnic Religionist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianEvangelicalChurch: Coding = {
  code: 'EVC',
  display: 'Christian: Evangelical Church',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianFriends: Coding = {
  code: 'FRQ',
  display: 'Christian: Friends',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianFullGospel: Coding = {
  code: 'FUL',
  display: 'Christian: Full Gospel',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianFreeWillBaptist: Coding = {
  code: 'FWB',
  display: 'Christian: Free Will Baptist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianGreekOrthodox: Coding = {
  code: 'GRE',
  display: 'Christian: Greek Orthodox',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Hindu: Coding = {
  code: 'HIN',
  display: 'Hindu',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_HinduOther: Coding = {
  code: 'HOT',
  display: 'Hindu: Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_HinduShaivites: Coding = {
  code: 'HSH',
  display: 'Hindu: Shaivites',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_HinduVaishnavites: Coding = {
  code: 'HVA',
  display: 'Hindu: Vaishnavites',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Jain: Coding = {
  code: 'JAI',
  display: 'Jain',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_JewishConservative: Coding = {
  code: 'JCO',
  display: 'Jewish: Conservative',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Jewish: Coding = {
  code: 'JEW',
  display: 'Jewish',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_JewishOrthodox: Coding = {
  code: 'JOR',
  display: 'Jewish: Orthodox',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_JewishOther: Coding = {
  code: 'JOT',
  display: 'Jewish: Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_JewishReconstructionist: Coding = {
  code: 'JRC',
  display: 'Jewish: Reconstructionist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_JewishReform: Coding = {
  code: 'JRF',
  display: 'Jewish: Reform',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_JewishRenewal: Coding = {
  code: 'JRN',
  display: 'Jewish: Renewal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianJehovahQuoteSWitness: Coding = {
  code: 'JWN',
  display: "Christian: Jehovah's Witness",
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianLutheranMissouriSynod: Coding = {
  code: 'LMS',
  display: 'Christian: Lutheran Missouri Synod',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianLutheran: Coding = {
  code: 'LUT',
  display: 'Christian: Lutheran',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianMennonite: Coding = {
  code: 'MEN',
  display: 'Christian: Mennonite',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianMethodist: Coding = {
  code: 'MET',
  display: 'Christian: Methodist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianLatterDaySaints: Coding = {
  code: 'MOM',
  display: 'Christian: Latter-day Saints',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Muslim: Coding = {
  code: 'MOS',
  display: 'Muslim',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_MuslimOther: Coding = {
  code: 'MOT',
  display: 'Muslim: Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_MuslimShiite: Coding = {
  code: 'MSH',
  display: 'Muslim: Shiite',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_MuslimSunni: Coding = {
  code: 'MSU',
  display: 'Muslim: Sunni',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_NativeAmerican: Coding = {
  code: 'NAM',
  display: 'Native American',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianChurchOfTheNazarene: Coding = {
  code: 'NAZ',
  display: 'Christian: Church of the Nazarene',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Nonreligious: Coding = {
  code: 'NOE',
  display: 'Nonreligious',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_NewReligionist: Coding = {
  code: 'NRL',
  display: 'New Religionist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianOrthodox: Coding = {
  code: 'ORT',
  display: 'Christian: Orthodox',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Other: Coding = {
  code: 'OTH',
  display: 'Other',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianPentecostal: Coding = {
  code: 'PEN',
  display: 'Christian: Pentecostal',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianOtherProtestant: Coding = {
  code: 'PRC',
  display: 'Christian: Other Protestant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianPresbyterian: Coding = {
  code: 'PRE',
  display: 'Christian: Presbyterian',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianProtestant: Coding = {
  code: 'PRO',
  display: 'Christian: Protestant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianReformedChurch: Coding = {
  code: 'REC',
  display: 'Christian: Reformed Church',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianReorganizedChurchOfJesusChristLDS: Coding = {
  code: 'REO',
  display: 'Christian: Reorganized Church of Jesus Christ-LDS',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianSalvationArmy: Coding = {
  code: 'SAA',
  display: 'Christian: Salvation Army',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianSeventhDayAdventist: Coding = {
  code: 'SEV',
  display: 'Christian: Seventh Day Adventist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Shintoist: Coding = {
  code: 'SHN',
  display: 'Shintoist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Sikh: Coding = {
  code: 'SIK',
  display: 'Sikh',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianSouthernBaptist: Coding = {
  code: 'SOU',
  display: 'Christian: Southern Baptist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Spiritist: Coding = {
  code: 'SPI',
  display: 'Spiritist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianUnitedChurchOfChrist: Coding = {
  code: 'UCC',
  display: 'Christian: United Church of Christ',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianUnitedMethodist: Coding = {
  code: 'UMD',
  display: 'Christian: United Methodist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianUnitarian: Coding = {
  code: 'UNI',
  display: 'Christian: Unitarian',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianUnitarianUniversalist: Coding = {
  code: 'UNU',
  display: 'Christian: Unitarian Universalist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_Unknown: Coding = {
  code: 'VAR',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianWesleyan: Coding = {
  code: 'WES',
  display: 'Christian: Wesleyan',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
const v2_2_4_0006_ChristianWesleyanMethodist: Coding = {
  code: 'WMC',
  display: 'Christian: Wesleyan Methodist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0006|2.4',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0006 ver 2.9 ( Religion)
 */
export const V2240006 = {
  ChristianAmericanBaptistChurch: v2_2_4_0006_ChristianAmericanBaptistChurch,
  Agnostic: v2_2_4_0006_Agnostic,
  ChristianAfricanMethodistEpiscopalZion: v2_2_4_0006_ChristianAfricanMethodistEpiscopalZion,
  ChristianAfricanMethodistEpiscopal: v2_2_4_0006_ChristianAfricanMethodistEpiscopal,
  ChristianAnglican: v2_2_4_0006_ChristianAnglican,
  ChristianAssemblyOfGod: v2_2_4_0006_ChristianAssemblyOfGod,
  Atheist: v2_2_4_0006_Atheist,
  BahaQuoteI: v2_2_4_0006_BahaQuoteI,
  ChristianBaptist: v2_2_4_0006_ChristianBaptist,
  BuddhistMahayana: v2_2_4_0006_BuddhistMahayana,
  BuddhistOther: v2_2_4_0006_BuddhistOther,
  Brethren: v2_2_4_0006_Brethren,
  BuddhistTantrayana: v2_2_4_0006_BuddhistTantrayana,
  BuddhistTheravada: v2_2_4_0006_BuddhistTheravada,
  Buddhist: v2_2_4_0006_Buddhist,
  ChristianRomanCatholic: v2_2_4_0006_ChristianRomanCatholic,
  ChineseFolkReligionist: v2_2_4_0006_ChineseFolkReligionist,
  Christian: v2_2_4_0006_Christian,
  ChristianChristianScience: v2_2_4_0006_ChristianChristianScience,
  ChristianChristianMissionaryAlliance: v2_2_4_0006_ChristianChristianMissionaryAlliance,
  Confucian: v2_2_4_0006_Confucian,
  ChristianChurchOfChrist: v2_2_4_0006_ChristianChurchOfChrist,
  ChristianChurchOfGod: v2_2_4_0006_ChristianChurchOfGod,
  ChristianChurchOfGodInChrist: v2_2_4_0006_ChristianChurchOfGodInChrist,
  ChristianCongregational: v2_2_4_0006_ChristianCongregational,
  ChristianCommunity: v2_2_4_0006_ChristianCommunity,
  ChristianOtherPentecostal: v2_2_4_0006_ChristianOtherPentecostal,
  ChristianOther: v2_2_4_0006_ChristianOther,
  ChristianChristianReformed: v2_2_4_0006_ChristianChristianReformed,
  DisciplesOfChrist: v2_2_4_0006_DisciplesOfChrist,
  ChristianEasternOrthodox: v2_2_4_0006_ChristianEasternOrthodox,
  ChristianEpiscopalian: v2_2_4_0006_ChristianEpiscopalian,
  EthnicReligionist: v2_2_4_0006_EthnicReligionist,
  ChristianEvangelicalChurch: v2_2_4_0006_ChristianEvangelicalChurch,
  ChristianFriends: v2_2_4_0006_ChristianFriends,
  ChristianFullGospel: v2_2_4_0006_ChristianFullGospel,
  ChristianFreeWillBaptist: v2_2_4_0006_ChristianFreeWillBaptist,
  ChristianGreekOrthodox: v2_2_4_0006_ChristianGreekOrthodox,
  Hindu: v2_2_4_0006_Hindu,
  HinduOther: v2_2_4_0006_HinduOther,
  HinduShaivites: v2_2_4_0006_HinduShaivites,
  HinduVaishnavites: v2_2_4_0006_HinduVaishnavites,
  Jain: v2_2_4_0006_Jain,
  JewishConservative: v2_2_4_0006_JewishConservative,
  Jewish: v2_2_4_0006_Jewish,
  JewishOrthodox: v2_2_4_0006_JewishOrthodox,
  JewishOther: v2_2_4_0006_JewishOther,
  JewishReconstructionist: v2_2_4_0006_JewishReconstructionist,
  JewishReform: v2_2_4_0006_JewishReform,
  JewishRenewal: v2_2_4_0006_JewishRenewal,
  ChristianJehovahQuoteSWitness: v2_2_4_0006_ChristianJehovahQuoteSWitness,
  ChristianLutheranMissouriSynod: v2_2_4_0006_ChristianLutheranMissouriSynod,
  ChristianLutheran: v2_2_4_0006_ChristianLutheran,
  ChristianMennonite: v2_2_4_0006_ChristianMennonite,
  ChristianMethodist: v2_2_4_0006_ChristianMethodist,
  ChristianLatterDaySaints: v2_2_4_0006_ChristianLatterDaySaints,
  Muslim: v2_2_4_0006_Muslim,
  MuslimOther: v2_2_4_0006_MuslimOther,
  MuslimShiite: v2_2_4_0006_MuslimShiite,
  MuslimSunni: v2_2_4_0006_MuslimSunni,
  NativeAmerican: v2_2_4_0006_NativeAmerican,
  ChristianChurchOfTheNazarene: v2_2_4_0006_ChristianChurchOfTheNazarene,
  Nonreligious: v2_2_4_0006_Nonreligious,
  NewReligionist: v2_2_4_0006_NewReligionist,
  ChristianOrthodox: v2_2_4_0006_ChristianOrthodox,
  Other: v2_2_4_0006_Other,
  ChristianPentecostal: v2_2_4_0006_ChristianPentecostal,
  ChristianOtherProtestant: v2_2_4_0006_ChristianOtherProtestant,
  ChristianPresbyterian: v2_2_4_0006_ChristianPresbyterian,
  ChristianProtestant: v2_2_4_0006_ChristianProtestant,
  ChristianReformedChurch: v2_2_4_0006_ChristianReformedChurch,
  ChristianReorganizedChurchOfJesusChristLDS: v2_2_4_0006_ChristianReorganizedChurchOfJesusChristLDS,
  ChristianSalvationArmy: v2_2_4_0006_ChristianSalvationArmy,
  ChristianSeventhDayAdventist: v2_2_4_0006_ChristianSeventhDayAdventist,
  Shintoist: v2_2_4_0006_Shintoist,
  Sikh: v2_2_4_0006_Sikh,
  ChristianSouthernBaptist: v2_2_4_0006_ChristianSouthernBaptist,
  Spiritist: v2_2_4_0006_Spiritist,
  ChristianUnitedChurchOfChrist: v2_2_4_0006_ChristianUnitedChurchOfChrist,
  ChristianUnitedMethodist: v2_2_4_0006_ChristianUnitedMethodist,
  ChristianUnitarian: v2_2_4_0006_ChristianUnitarian,
  ChristianUnitarianUniversalist: v2_2_4_0006_ChristianUnitarianUniversalist,
  Unknown: v2_2_4_0006_Unknown,
  ChristianWesleyan: v2_2_4_0006_ChristianWesleyan,
  ChristianWesleyanMethodist: v2_2_4_0006_ChristianWesleyanMethodist,
};
const v2_2_6_0391_ADMINISTRATION: Coding = {
  code: 'ADMINISTRATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ALLERGY: Coding = {
  code: 'ALLERGY',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_APPSTATS: Coding = {
  code: 'APP_STATS',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_APPSTATUS: Coding = {
  code: 'APP_STATUS',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ASSOCIATEDPERSON: Coding = {
  code: 'ASSOCIATED_PERSON',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ASSOCIATEDRXADMIN: Coding = {
  code: 'ASSOCIATED_RX_ADMIN',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ASSOCIATEDRXORDER: Coding = {
  code: 'ASSOCIATED_RX_ORDER',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_AUTHORIZATION: Coding = {
  code: 'AUTHORIZATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_AUTHORIZATIONCONTACT: Coding = {
  code: 'AUTHORIZATION_CONTACT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_CERTIFICATE: Coding = {
  code: 'CERTIFICATE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_CLOCK: Coding = {
  code: 'CLOCK',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_CLOCKANDSTATISTICS: Coding = {
  code: 'CLOCK_AND_STATISTICS',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_CLOCKANDSTATSWITHNOTES: Coding = {
  code: 'CLOCK_AND_STATS_WITH_NOTES',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_CLOCKANDSTATSWITHNOTESALT: Coding = {
  code: 'CLOCK_AND_STATS_WITH_NOTES_ALT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_COMMAND: Coding = {
  code: 'COMMAND',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_COMMANDRESPONSE: Coding = {
  code: 'COMMAND_RESPONSE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_COMMONORDER: Coding = {
  code: 'COMMON_ORDER',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_COMPONENT: Coding = {
  code: 'COMPONENT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_COMPONENTS: Coding = {
  code: 'COMPONENTS',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_CONTAINER: Coding = {
  code: 'CONTAINER',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_DEFINITION: Coding = {
  code: 'DEFINITION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_DIET: Coding = {
  code: 'DIET',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_DISPENSE: Coding = {
  code: 'DISPENSE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ENCODEDORDER: Coding = {
  code: 'ENCODED_ORDER',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ENCODING: Coding = {
  code: 'ENCODING',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_EXPERIENCE: Coding = {
  code: 'EXPERIENCE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_FINANCIAL: Coding = {
  code: 'FINANCIAL',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_FINANCIALCOMMONORDER: Coding = {
  code: 'FINANCIAL_COMMON_ORDER',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_FINANCIALINSURANCE: Coding = {
  code: 'FINANCIAL_INSURANCE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_FINANCIALOBSERVATION: Coding = {
  code: 'FINANCIAL_OBSERVATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_FINANCIALORDER: Coding = {
  code: 'FINANCIAL_ORDER',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_FINANCIALPROCEDURE: Coding = {
  code: 'FINANCIAL_PROCEDURE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_FINANCIALTIMINGQUANTITY: Coding = {
  code: 'FINANCIAL_TIMING_QUANTITY',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_GENERALRESOURCE: Coding = {
  code: 'GENERAL_RESOURCE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_GIVE: Coding = {
  code: 'GIVE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_GOAL: Coding = {
  code: 'GOAL',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_GOALOBSERVATION: Coding = {
  code: 'GOAL_OBSERVATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_GOALPATHWAY: Coding = {
  code: 'GOAL_PATHWAY',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_GOALROLE: Coding = {
  code: 'GOAL_ROLE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_GUARANTORINSURANCE: Coding = {
  code: 'GUARANTOR_INSURANCE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_INSURANCE: Coding = {
  code: 'INSURANCE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_LOCATIONRESOURCE: Coding = {
  code: 'LOCATION_RESOURCE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MERGEINFO: Coding = {
  code: 'MERGE_INFO',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MF: Coding = {
  code: 'MF',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFCDM: Coding = {
  code: 'MF_CDM',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFCLINSTUDY: Coding = {
  code: 'MF_CLIN_STUDY',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFCLINSTUDYSCHED: Coding = {
  code: 'MF_CLIN_STUDY_SCHED',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFINVITEM: Coding = {
  code: 'MF_INV_ITEM',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFLOCDEPT: Coding = {
  code: 'MF_LOC_DEPT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFLOCATION: Coding = {
  code: 'MF_LOCATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFOBSATTRIBUTES: Coding = {
  code: 'MF_OBS_ATTRIBUTES',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFPHASESCHEDDETAIL: Coding = {
  code: 'MF_PHASE_SCHED_DETAIL',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFQUERY: Coding = {
  code: 'MF_QUERY',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFSITEDEFINED: Coding = {
  code: 'MF_SITE_DEFINED',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFSTAFF: Coding = {
  code: 'MF_STAFF',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFTEST: Coding = {
  code: 'MF_TEST',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFTESTBATTDETAIL: Coding = {
  code: 'MF_TEST_BATT_DETAIL',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFTESTBATTERIES: Coding = {
  code: 'MF_TEST_BATTERIES',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFTESTCALCDETAIL: Coding = {
  code: 'MF_TEST_CALC_DETAIL',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFTESTCALCULATED: Coding = {
  code: 'MF_TEST_CALCULATED',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFTESTCATDETAIL: Coding = {
  code: 'MF_TEST_CAT_DETAIL',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFTESTCATEGORICAL: Coding = {
  code: 'MF_TEST_CATEGORICAL',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_MFTESTNUMERIC: Coding = {
  code: 'MF_TEST_NUMERIC',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_NK1TIMINGQTY: Coding = {
  code: 'NK1_TIMING_QTY',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_NOTIFICATION: Coding = {
  code: 'NOTIFICATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_OBSERVATION: Coding = {
  code: 'OBSERVATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_OBSERVATIONPRIOR: Coding = {
  code: 'OBSERVATION_PRIOR',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_OBSERVATIONREQUEST: Coding = {
  code: 'OBSERVATION_REQUEST',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_OMSERVATION: Coding = {
  code: 'OMSERVATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ORDER: Coding = {
  code: 'ORDER',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ORDERCHOICE: Coding = {
  code: 'ORDER_CHOICE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ORDERDETAIL: Coding = {
  code: 'ORDER_DETAIL',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ORDERDETAILSUPPLEMENT: Coding = {
  code: 'ORDER_DETAIL_SUPPLEMENT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ORDERDIET: Coding = {
  code: 'ORDER_DIET',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ORDERENCODED: Coding = {
  code: 'ORDER_ENCODED',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ORDEROBSERVATION: Coding = {
  code: 'ORDER_OBSERVATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ORDERPRIOR: Coding = {
  code: 'ORDER_PRIOR',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ORDERTRAY: Coding = {
  code: 'ORDER_TRAY',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PATHWAY: Coding = {
  code: 'PATHWAY',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PATHWAYROLE: Coding = {
  code: 'PATHWAY_ROLE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PATIENT: Coding = {
  code: 'PATIENT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PATIENTPRIOR: Coding = {
  code: 'PATIENT_PRIOR',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PATIENTRESULT: Coding = {
  code: 'PATIENT_RESULT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PATIENTVISIT: Coding = {
  code: 'PATIENT_VISIT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PATIENTVISITPRIOR: Coding = {
  code: 'PATIENT_VISIT_PRIOR',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PERSONNELRESOURCE: Coding = {
  code: 'PERSONNEL_RESOURCE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PEXCAUSE: Coding = {
  code: 'PEX_CAUSE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PEXOBSERVATION: Coding = {
  code: 'PEX_OBSERVATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PRIORRESULT: Coding = {
  code: 'PRIOR_RESULT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PROBLEM: Coding = {
  code: 'PROBLEM',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PROBLEMOBSERVATION: Coding = {
  code: 'PROBLEM_OBSERVATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PROBLEMPATHWAY: Coding = {
  code: 'PROBLEM_PATHWAY',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PROBLEMROLE: Coding = {
  code: 'PROBLEM_ROLE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PROCEDURE: Coding = {
  code: 'PROCEDURE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PRODUCT: Coding = {
  code: 'PRODUCT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PRODUCTSTATUS: Coding = {
  code: 'PRODUCT_STATUS',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PROVIDER: Coding = {
  code: 'PROVIDER',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_PROVIDERCONTACT: Coding = {
  code: 'PROVIDER_CONTACT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_QBP: Coding = {
  code: 'QBP',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_QRYWITHDETAIL: Coding = {
  code: 'QRY_WITH_DETAIL',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_QUERYRESPONSE: Coding = {
  code: 'QUERY_RESPONSE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_QUERYRESULTCLUSTER: Coding = {
  code: 'QUERY_RESULT_CLUSTER',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_REQUEST: Coding = {
  code: 'REQUEST',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_RESOURCE: Coding = {
  code: 'RESOURCE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_RESOURCES: Coding = {
  code: 'RESOURCES',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_RESPONSE: Coding = {
  code: 'RESPONSE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_RESULT: Coding = {
  code: 'RESULT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_RESULTS: Coding = {
  code: 'RESULTS',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_RESULTSNOTES: Coding = {
  code: 'RESULTS_NOTES',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_ROWDEFINITION: Coding = {
  code: 'ROW_DEFINITION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_RXADMINISTRATION: Coding = {
  code: 'RX_ADMINISTRATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_RXORDER: Coding = {
  code: 'RX_ORDER',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_SCHEDULE: Coding = {
  code: 'SCHEDULE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_SERVICE: Coding = {
  code: 'SERVICE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_SPECIMEN: Coding = {
  code: 'SPECIMEN',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_SPECIMENCONTAINER: Coding = {
  code: 'SPECIMEN_CONTAINER',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_STAFF: Coding = {
  code: 'STAFF',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_STUDY: Coding = {
  code: 'STUDY',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_STUDYOBSERVATION: Coding = {
  code: 'STUDY_OBSERVATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_STUDYPHASE: Coding = {
  code: 'STUDY_PHASE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_STUDYSCHEDULE: Coding = {
  code: 'STUDY_SCHEDULE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_TESTCONFIGURATION: Coding = {
  code: 'TEST_CONFIGURATION',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_TIMING: Coding = {
  code: 'TIMING',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_TIMINGDIET: Coding = {
  code: 'TIMING_DIET',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_TIMINGENCODED: Coding = {
  code: 'TIMING_ENCODED',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_TIMINGGIVE: Coding = {
  code: 'TIMING_GIVE',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_TIMINGPRIOR: Coding = {
  code: 'TIMING_PRIOR',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_TIMINGQTY: Coding = {
  code: 'TIMING_QTY',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_TIMINGQUANTITY: Coding = {
  code: 'TIMING_QUANTITY',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_TIMINGTRAY: Coding = {
  code: 'TIMING_TRAY',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_TREATMENT: Coding = {
  code: 'TREATMENT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
const v2_2_6_0391_VISIT: Coding = {
  code: 'VISIT',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0391|2.6',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0391 ver 2.9 ( Segment Group)
 */
export const V2260391 = {
  ADMINISTRATION: v2_2_6_0391_ADMINISTRATION,
  ALLERGY: v2_2_6_0391_ALLERGY,
  APPSTATS: v2_2_6_0391_APPSTATS,
  APPSTATUS: v2_2_6_0391_APPSTATUS,
  ASSOCIATEDPERSON: v2_2_6_0391_ASSOCIATEDPERSON,
  ASSOCIATEDRXADMIN: v2_2_6_0391_ASSOCIATEDRXADMIN,
  ASSOCIATEDRXORDER: v2_2_6_0391_ASSOCIATEDRXORDER,
  AUTHORIZATION: v2_2_6_0391_AUTHORIZATION,
  AUTHORIZATIONCONTACT: v2_2_6_0391_AUTHORIZATIONCONTACT,
  CERTIFICATE: v2_2_6_0391_CERTIFICATE,
  CLOCK: v2_2_6_0391_CLOCK,
  CLOCKANDSTATISTICS: v2_2_6_0391_CLOCKANDSTATISTICS,
  CLOCKANDSTATSWITHNOTES: v2_2_6_0391_CLOCKANDSTATSWITHNOTES,
  CLOCKANDSTATSWITHNOTESALT: v2_2_6_0391_CLOCKANDSTATSWITHNOTESALT,
  COMMAND: v2_2_6_0391_COMMAND,
  COMMANDRESPONSE: v2_2_6_0391_COMMANDRESPONSE,
  COMMONORDER: v2_2_6_0391_COMMONORDER,
  COMPONENT: v2_2_6_0391_COMPONENT,
  COMPONENTS: v2_2_6_0391_COMPONENTS,
  CONTAINER: v2_2_6_0391_CONTAINER,
  DEFINITION: v2_2_6_0391_DEFINITION,
  DIET: v2_2_6_0391_DIET,
  DISPENSE: v2_2_6_0391_DISPENSE,
  ENCODEDORDER: v2_2_6_0391_ENCODEDORDER,
  ENCODING: v2_2_6_0391_ENCODING,
  EXPERIENCE: v2_2_6_0391_EXPERIENCE,
  FINANCIAL: v2_2_6_0391_FINANCIAL,
  FINANCIALCOMMONORDER: v2_2_6_0391_FINANCIALCOMMONORDER,
  FINANCIALINSURANCE: v2_2_6_0391_FINANCIALINSURANCE,
  FINANCIALOBSERVATION: v2_2_6_0391_FINANCIALOBSERVATION,
  FINANCIALORDER: v2_2_6_0391_FINANCIALORDER,
  FINANCIALPROCEDURE: v2_2_6_0391_FINANCIALPROCEDURE,
  FINANCIALTIMINGQUANTITY: v2_2_6_0391_FINANCIALTIMINGQUANTITY,
  GENERALRESOURCE: v2_2_6_0391_GENERALRESOURCE,
  GIVE: v2_2_6_0391_GIVE,
  GOAL: v2_2_6_0391_GOAL,
  GOALOBSERVATION: v2_2_6_0391_GOALOBSERVATION,
  GOALPATHWAY: v2_2_6_0391_GOALPATHWAY,
  GOALROLE: v2_2_6_0391_GOALROLE,
  GUARANTORINSURANCE: v2_2_6_0391_GUARANTORINSURANCE,
  INSURANCE: v2_2_6_0391_INSURANCE,
  LOCATIONRESOURCE: v2_2_6_0391_LOCATIONRESOURCE,
  MERGEINFO: v2_2_6_0391_MERGEINFO,
  MF: v2_2_6_0391_MF,
  MFCDM: v2_2_6_0391_MFCDM,
  MFCLINSTUDY: v2_2_6_0391_MFCLINSTUDY,
  MFCLINSTUDYSCHED: v2_2_6_0391_MFCLINSTUDYSCHED,
  MFINVITEM: v2_2_6_0391_MFINVITEM,
  MFLOCDEPT: v2_2_6_0391_MFLOCDEPT,
  MFLOCATION: v2_2_6_0391_MFLOCATION,
  MFOBSATTRIBUTES: v2_2_6_0391_MFOBSATTRIBUTES,
  MFPHASESCHEDDETAIL: v2_2_6_0391_MFPHASESCHEDDETAIL,
  MFQUERY: v2_2_6_0391_MFQUERY,
  MFSITEDEFINED: v2_2_6_0391_MFSITEDEFINED,
  MFSTAFF: v2_2_6_0391_MFSTAFF,
  MFTEST: v2_2_6_0391_MFTEST,
  MFTESTBATTDETAIL: v2_2_6_0391_MFTESTBATTDETAIL,
  MFTESTBATTERIES: v2_2_6_0391_MFTESTBATTERIES,
  MFTESTCALCDETAIL: v2_2_6_0391_MFTESTCALCDETAIL,
  MFTESTCALCULATED: v2_2_6_0391_MFTESTCALCULATED,
  MFTESTCATDETAIL: v2_2_6_0391_MFTESTCATDETAIL,
  MFTESTCATEGORICAL: v2_2_6_0391_MFTESTCATEGORICAL,
  MFTESTNUMERIC: v2_2_6_0391_MFTESTNUMERIC,
  NK1TIMINGQTY: v2_2_6_0391_NK1TIMINGQTY,
  NOTIFICATION: v2_2_6_0391_NOTIFICATION,
  OBSERVATION: v2_2_6_0391_OBSERVATION,
  OBSERVATIONPRIOR: v2_2_6_0391_OBSERVATIONPRIOR,
  OBSERVATIONREQUEST: v2_2_6_0391_OBSERVATIONREQUEST,
  OMSERVATION: v2_2_6_0391_OMSERVATION,
  ORDER: v2_2_6_0391_ORDER,
  ORDERCHOICE: v2_2_6_0391_ORDERCHOICE,
  ORDERDETAIL: v2_2_6_0391_ORDERDETAIL,
  ORDERDETAILSUPPLEMENT: v2_2_6_0391_ORDERDETAILSUPPLEMENT,
  ORDERDIET: v2_2_6_0391_ORDERDIET,
  ORDERENCODED: v2_2_6_0391_ORDERENCODED,
  ORDEROBSERVATION: v2_2_6_0391_ORDEROBSERVATION,
  ORDERPRIOR: v2_2_6_0391_ORDERPRIOR,
  ORDERTRAY: v2_2_6_0391_ORDERTRAY,
  PATHWAY: v2_2_6_0391_PATHWAY,
  PATHWAYROLE: v2_2_6_0391_PATHWAYROLE,
  PATIENT: v2_2_6_0391_PATIENT,
  PATIENTPRIOR: v2_2_6_0391_PATIENTPRIOR,
  PATIENTRESULT: v2_2_6_0391_PATIENTRESULT,
  PATIENTVISIT: v2_2_6_0391_PATIENTVISIT,
  PATIENTVISITPRIOR: v2_2_6_0391_PATIENTVISITPRIOR,
  PERSONNELRESOURCE: v2_2_6_0391_PERSONNELRESOURCE,
  PEXCAUSE: v2_2_6_0391_PEXCAUSE,
  PEXOBSERVATION: v2_2_6_0391_PEXOBSERVATION,
  PRIORRESULT: v2_2_6_0391_PRIORRESULT,
  PROBLEM: v2_2_6_0391_PROBLEM,
  PROBLEMOBSERVATION: v2_2_6_0391_PROBLEMOBSERVATION,
  PROBLEMPATHWAY: v2_2_6_0391_PROBLEMPATHWAY,
  PROBLEMROLE: v2_2_6_0391_PROBLEMROLE,
  PROCEDURE: v2_2_6_0391_PROCEDURE,
  PRODUCT: v2_2_6_0391_PRODUCT,
  PRODUCTSTATUS: v2_2_6_0391_PRODUCTSTATUS,
  PROVIDER: v2_2_6_0391_PROVIDER,
  PROVIDERCONTACT: v2_2_6_0391_PROVIDERCONTACT,
  QBP: v2_2_6_0391_QBP,
  QRYWITHDETAIL: v2_2_6_0391_QRYWITHDETAIL,
  QUERYRESPONSE: v2_2_6_0391_QUERYRESPONSE,
  QUERYRESULTCLUSTER: v2_2_6_0391_QUERYRESULTCLUSTER,
  REQUEST: v2_2_6_0391_REQUEST,
  RESOURCE: v2_2_6_0391_RESOURCE,
  RESOURCES: v2_2_6_0391_RESOURCES,
  RESPONSE: v2_2_6_0391_RESPONSE,
  RESULT: v2_2_6_0391_RESULT,
  RESULTS: v2_2_6_0391_RESULTS,
  RESULTSNOTES: v2_2_6_0391_RESULTSNOTES,
  ROWDEFINITION: v2_2_6_0391_ROWDEFINITION,
  RXADMINISTRATION: v2_2_6_0391_RXADMINISTRATION,
  RXORDER: v2_2_6_0391_RXORDER,
  SCHEDULE: v2_2_6_0391_SCHEDULE,
  SERVICE: v2_2_6_0391_SERVICE,
  SPECIMEN: v2_2_6_0391_SPECIMEN,
  SPECIMENCONTAINER: v2_2_6_0391_SPECIMENCONTAINER,
  STAFF: v2_2_6_0391_STAFF,
  STUDY: v2_2_6_0391_STUDY,
  STUDYOBSERVATION: v2_2_6_0391_STUDYOBSERVATION,
  STUDYPHASE: v2_2_6_0391_STUDYPHASE,
  STUDYSCHEDULE: v2_2_6_0391_STUDYSCHEDULE,
  TESTCONFIGURATION: v2_2_6_0391_TESTCONFIGURATION,
  TIMING: v2_2_6_0391_TIMING,
  TIMINGDIET: v2_2_6_0391_TIMINGDIET,
  TIMINGENCODED: v2_2_6_0391_TIMINGENCODED,
  TIMINGGIVE: v2_2_6_0391_TIMINGGIVE,
  TIMINGPRIOR: v2_2_6_0391_TIMINGPRIOR,
  TIMINGQTY: v2_2_6_0391_TIMINGQTY,
  TIMINGQUANTITY: v2_2_6_0391_TIMINGQUANTITY,
  TIMINGTRAY: v2_2_6_0391_TIMINGTRAY,
  TREATMENT: v2_2_6_0391_TREATMENT,
  VISIT: v2_2_6_0391_VISIT,
};
const v2_2_7_0360_AssociateOfArts: Coding = {
  code: 'AA',
  display: 'Associate of Arts',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_AssociateOfAppliedScience: Coding = {
  code: 'AAS',
  display: 'Associate of Applied Science',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_AssociateOfBusinessAdministration: Coding = {
  code: 'ABA',
  display: 'Associate of Business Administration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_AssociateOfEngineering: Coding = {
  code: 'AE',
  display: 'Associate of Engineering',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_AssociateOfScience: Coding = {
  code: 'AS',
  display: 'Associate of Science',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_BachelorOfArts: Coding = {
  code: 'BA',
  display: 'Bachelor of Arts',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_BachelorOfBusinessAdministration: Coding = {
  code: 'BBA',
  display: 'Bachelor of Business Administration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_BachelorOrEngineering: Coding = {
  code: 'BE',
  display: 'Bachelor or Engineering',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_BachelorOfFineArts: Coding = {
  code: 'BFA',
  display: 'Bachelor of Fine Arts',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_BachelorOfNursing: Coding = {
  code: 'BN',
  display: 'Bachelor of Nursing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_BachelorOfScience: Coding = {
  code: 'BS',
  display: 'Bachelor of Science',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_BachelorOfScienceLaw: Coding = {
  code: 'BSL',
  display: 'Bachelor of Science - Law',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_BachelorOnScienceNursing: Coding = {
  code: 'BSN',
  display: 'Bachelor on Science - Nursing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_BachelorOfTheology: Coding = {
  code: 'BT',
  display: 'Bachelor of Theology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_CertifiedAdultNursePractitioner: Coding = {
  code: 'CANP',
  display: 'Certified Adult Nurse Practitioner',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_Certificate: Coding = {
  code: 'CER',
  display: 'Certificate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_CertifiedMedicalAssistant: Coding = {
  code: 'CMA',
  display: 'Certified Medical Assistant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_CertifiedNurseMidwife: Coding = {
  code: 'CNM',
  display: 'Certified Nurse Midwife',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_CertifiedNursePractitioner: Coding = {
  code: 'CNP',
  display: 'Certified Nurse Practitioner',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_CertifiedNurseSpecialist: Coding = {
  code: 'CNS',
  display: 'Certified Nurse Specialist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_CertifiedPediatricNursePractitioner: Coding = {
  code: 'CPNP',
  display: 'Certified Pediatric Nurse Practitioner',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_CertifiedRegisteredNurse: Coding = {
  code: 'CRN',
  display: 'Certified Registered Nurse',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_CertifiedTumorRegistrar: Coding = {
  code: 'CTR',
  display: 'Certified Tumor Registrar',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_DoctorOfBusinessAdministration: Coding = {
  code: 'DBA',
  display: 'Doctor of Business Administration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_DoctorOfEducation: Coding = {
  code: 'DED',
  display: 'Doctor of Education',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_Diploma: Coding = {
  code: 'DIP',
  display: 'Diploma',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_DoctorOfOsteopathy: Coding = {
  code: 'DO',
  display: 'Doctor of Osteopathy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_EmergencyMedicalTechnician: Coding = {
  code: 'EMT',
  display: 'Emergency Medical Technician',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_EmergencyMedicalTechnicianParamedic: Coding = {
  code: 'EMTP',
  display: 'Emergency Medical Technician - Paramedic',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_FamilyPracticeNursePractitioner: Coding = {
  code: 'FPNP',
  display: 'Family Practice Nurse Practitioner',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_HighSchoolGraduate: Coding = {
  code: 'HS',
  display: 'High School Graduate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_JurisDoctor: Coding = {
  code: 'JD',
  display: 'Juris Doctor',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MasterOfArts: Coding = {
  code: 'MA',
  display: 'Master of Arts',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MasterOfBusinessAdministration: Coding = {
  code: 'MBA',
  display: 'Master of Business Administration',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MasterOfCivilEngineering: Coding = {
  code: 'MCE',
  display: 'Master of Civil Engineering',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_DoctorOfMedicine: Coding = {
  code: 'MD',
  display: 'Doctor of Medicine',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MedicalAssistant: Coding = {
  code: 'MDA',
  display: 'Medical Assistant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MasterOfDivinity: Coding = {
  code: 'MDI',
  display: 'Master of Divinity',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MasterOfEngineering: Coding = {
  code: 'ME',
  display: 'Master of Engineering',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MasterOfEducation: Coding = {
  code: 'MED',
  display: 'Master of Education',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MasterOfElectricalEngineering: Coding = {
  code: 'MEE',
  display: 'Master of Electrical Engineering',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MasterOfFineArts: Coding = {
  code: 'MFA',
  display: 'Master of Fine Arts',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MasterOfMechanicalEngineering: Coding = {
  code: 'MME',
  display: 'Master of Mechanical Engineering',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MasterOfScience: Coding = {
  code: 'MS',
  display: 'Master of Science',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MasterOfScienceLaw: Coding = {
  code: 'MSL',
  display: 'Master of Science - Law',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MasterOfScienceNursing: Coding = {
  code: 'MSN',
  display: 'Master of Science - Nursing',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MedicalTechnician: Coding = {
  code: 'MT',
  display: 'Medical Technician',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_MasterOfTheology: Coding = {
  code: 'MTH',
  display: 'Master of Theology',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_NonGraduate: Coding = {
  code: 'NG',
  display: 'Non-Graduate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_NursePractitioner: Coding = {
  code: 'NP',
  display: 'Nurse Practitioner',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_PhysicianAssistant: Coding = {
  code: 'PA',
  display: 'Physician Assistant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_DoctorOfPharmacy: Coding = {
  code: 'PharmD',
  display: 'Doctor of Pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_DoctorOfPhilosophy: Coding = {
  code: 'PHD',
  display: 'Doctor of Philosophy',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_DoctorOfEngineering: Coding = {
  code: 'PHE',
  display: 'Doctor of Engineering',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_DoctorOfScience: Coding = {
  code: 'PHS',
  display: 'Doctor of Science',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_AdvancedPracticeNurse: Coding = {
  code: 'PN',
  display: 'Advanced Practice Nurse',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_RegisteredMedicalAssistant: Coding = {
  code: 'RMA',
  display: 'Registered Medical Assistant',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_RegisteredNurse: Coding = {
  code: 'RN',
  display: 'Registered Nurse',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_RegisteredPharmacist: Coding = {
  code: 'RPH',
  display: 'Registered Pharmacist',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_SecretarialCertificate: Coding = {
  code: 'SEC',
  display: 'Secretarial Certificate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
const v2_2_7_0360_TradeSchoolGraduate: Coding = {
  code: 'TS',
  display: 'Trade School Graduate',
  system: 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7',
};
/**
 * FHIR Value set/code system definition for HL7 v2 table 0360 ver 2.9 ( Degree/License/Certificate)
 */
export const V2270360 = {
  AssociateOfArts: v2_2_7_0360_AssociateOfArts,
  AssociateOfAppliedScience: v2_2_7_0360_AssociateOfAppliedScience,
  AssociateOfBusinessAdministration: v2_2_7_0360_AssociateOfBusinessAdministration,
  AssociateOfEngineering: v2_2_7_0360_AssociateOfEngineering,
  AssociateOfScience: v2_2_7_0360_AssociateOfScience,
  BachelorOfArts: v2_2_7_0360_BachelorOfArts,
  BachelorOfBusinessAdministration: v2_2_7_0360_BachelorOfBusinessAdministration,
  BachelorOrEngineering: v2_2_7_0360_BachelorOrEngineering,
  BachelorOfFineArts: v2_2_7_0360_BachelorOfFineArts,
  BachelorOfNursing: v2_2_7_0360_BachelorOfNursing,
  BachelorOfScience: v2_2_7_0360_BachelorOfScience,
  BachelorOfScienceLaw: v2_2_7_0360_BachelorOfScienceLaw,
  BachelorOnScienceNursing: v2_2_7_0360_BachelorOnScienceNursing,
  BachelorOfTheology: v2_2_7_0360_BachelorOfTheology,
  CertifiedAdultNursePractitioner: v2_2_7_0360_CertifiedAdultNursePractitioner,
  Certificate: v2_2_7_0360_Certificate,
  CertifiedMedicalAssistant: v2_2_7_0360_CertifiedMedicalAssistant,
  CertifiedNurseMidwife: v2_2_7_0360_CertifiedNurseMidwife,
  CertifiedNursePractitioner: v2_2_7_0360_CertifiedNursePractitioner,
  CertifiedNurseSpecialist: v2_2_7_0360_CertifiedNurseSpecialist,
  CertifiedPediatricNursePractitioner: v2_2_7_0360_CertifiedPediatricNursePractitioner,
  CertifiedRegisteredNurse: v2_2_7_0360_CertifiedRegisteredNurse,
  CertifiedTumorRegistrar: v2_2_7_0360_CertifiedTumorRegistrar,
  DoctorOfBusinessAdministration: v2_2_7_0360_DoctorOfBusinessAdministration,
  DoctorOfEducation: v2_2_7_0360_DoctorOfEducation,
  Diploma: v2_2_7_0360_Diploma,
  DoctorOfOsteopathy: v2_2_7_0360_DoctorOfOsteopathy,
  EmergencyMedicalTechnician: v2_2_7_0360_EmergencyMedicalTechnician,
  EmergencyMedicalTechnicianParamedic: v2_2_7_0360_EmergencyMedicalTechnicianParamedic,
  FamilyPracticeNursePractitioner: v2_2_7_0360_FamilyPracticeNursePractitioner,
  HighSchoolGraduate: v2_2_7_0360_HighSchoolGraduate,
  JurisDoctor: v2_2_7_0360_JurisDoctor,
  MasterOfArts: v2_2_7_0360_MasterOfArts,
  MasterOfBusinessAdministration: v2_2_7_0360_MasterOfBusinessAdministration,
  MasterOfCivilEngineering: v2_2_7_0360_MasterOfCivilEngineering,
  DoctorOfMedicine: v2_2_7_0360_DoctorOfMedicine,
  MedicalAssistant: v2_2_7_0360_MedicalAssistant,
  MasterOfDivinity: v2_2_7_0360_MasterOfDivinity,
  MasterOfEngineering: v2_2_7_0360_MasterOfEngineering,
  MasterOfEducation: v2_2_7_0360_MasterOfEducation,
  MasterOfElectricalEngineering: v2_2_7_0360_MasterOfElectricalEngineering,
  MasterOfFineArts: v2_2_7_0360_MasterOfFineArts,
  MasterOfMechanicalEngineering: v2_2_7_0360_MasterOfMechanicalEngineering,
  MasterOfScience: v2_2_7_0360_MasterOfScience,
  MasterOfScienceLaw: v2_2_7_0360_MasterOfScienceLaw,
  MasterOfScienceNursing: v2_2_7_0360_MasterOfScienceNursing,
  MedicalTechnician: v2_2_7_0360_MedicalTechnician,
  MasterOfTheology: v2_2_7_0360_MasterOfTheology,
  NonGraduate: v2_2_7_0360_NonGraduate,
  NursePractitioner: v2_2_7_0360_NursePractitioner,
  PhysicianAssistant: v2_2_7_0360_PhysicianAssistant,
  DoctorOfPharmacy: v2_2_7_0360_DoctorOfPharmacy,
  DoctorOfPhilosophy: v2_2_7_0360_DoctorOfPhilosophy,
  DoctorOfEngineering: v2_2_7_0360_DoctorOfEngineering,
  DoctorOfScience: v2_2_7_0360_DoctorOfScience,
  AdvancedPracticeNurse: v2_2_7_0360_AdvancedPracticeNurse,
  RegisteredMedicalAssistant: v2_2_7_0360_RegisteredMedicalAssistant,
  RegisteredNurse: v2_2_7_0360_RegisteredNurse,
  RegisteredPharmacist: v2_2_7_0360_RegisteredPharmacist,
  SecretarialCertificate: v2_2_7_0360_SecretarialCertificate,
  TradeSchoolGraduate: v2_2_7_0360_TradeSchoolGraduate,
};
const v3_AcknowledgementCondition_Always: Coding = {
  code: 'AL',
  display: 'Always',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementCondition',
};
const v3_AcknowledgementCondition_ErrorRejectOnly: Coding = {
  code: 'ER',
  display: 'Error/reject only',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementCondition',
};
const v3_AcknowledgementCondition_Never: Coding = {
  code: 'NE',
  display: 'Never',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementCondition',
};
const v3_AcknowledgementCondition_SuccessfulOnly: Coding = {
  code: 'SU',
  display: 'Successful only',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementCondition',
};
/**
 *  The codes identify the conditions under which accept acknowledgements are required to be returned in response to this message. Note that accept acknowledgement address two different issues at the same time: reliable transport as well as syntactical correctness
 */
export const V3AcknowledgementCondition = {
  /**
   * Always send an acknowledgement.
   */
  Always: v3_AcknowledgementCondition_Always,
  /**
   * Send an acknowledgement for error/reject conditions only.
   */
  ErrorRejectOnly: v3_AcknowledgementCondition_ErrorRejectOnly,
  /**
   * Never send an acknowledgement.
   */
  Never: v3_AcknowledgementCondition_Never,
  /**
   * Send an acknowledgement for successful completions only.
   */
  SuccessfulOnly: v3_AcknowledgementCondition_SuccessfulOnly,
};
const v3_AcknowledgementDetailCode_AcknowledgementDetailNotSupportedCode: Coding = {
  code: '_AcknowledgementDetailNotSupportedCode',
  display: 'AcknowledgementDetailNotSupportedCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_InternalSystemError: Coding = {
  code: 'INTERR',
  display: 'Internal system error',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_NoStorageSpaceForMessage: Coding = {
  code: 'NOSTORE',
  display: 'No storage space for message.',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_UnsupportedInteraction: Coding = {
  code: 'NS200',
  display: 'Unsupported interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_UnsupportedProcessingId: Coding = {
  code: 'NS202',
  display: 'Unsupported processing id',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_UnsupportedVersionId: Coding = {
  code: 'NS203',
  display: 'Unsupported version id',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_UnsupportedProcessingMode: Coding = {
  code: 'NS250',
  display: 'Unsupported processing Mode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_UnknownSender: Coding = {
  code: 'NS260',
  display: 'Unknown sender',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_UnrecognizedAttentionline: Coding = {
  code: 'NS261',
  display: 'Unrecognized attentionline',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_MessageRoutingErrorDestinationUnreachable: Coding = {
  code: 'RTEDEST',
  display: 'Message routing error, destination unreachable.',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_ErrorMessageRoutingErrorUnknownDestination: Coding = {
  code: 'RTUDEST',
  display: 'Error: Message routing error, unknown destination.',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_MessageRoutingWarningDestinationUnreachable: Coding = {
  code: 'RTWDEST',
  display: 'Message routing warning, destination unreachable.',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_SyntaxError: Coding = {
  code: 'SYN',
  display: 'Syntax error',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_RequiredAssociationMissing: Coding = {
  code: 'SYN100',
  display: 'Required association missing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_RequiredAttributeMissing: Coding = {
  code: 'SYN101',
  display: 'Required attribute missing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_DataTypeError: Coding = {
  code: 'SYN102',
  display: 'Data type error',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_ValueNotFoundInCodeSystem: Coding = {
  code: 'SYN103',
  display: 'Value not found in code system',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_InvalidCodeSystemInCNE: Coding = {
  code: 'SYN104',
  display: 'Invalid code system in CNE',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_RequiredElementMissing: Coding = {
  code: 'SYN105',
  display: 'Required element missing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_TerminologyError: Coding = {
  code: 'SYN106',
  display: 'Terminology error',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_DeprecatedCode: Coding = {
  code: 'SYN107',
  display: 'Deprecated code',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_NumberOfRepetitionsExceedsLimit: Coding = {
  code: 'SYN108',
  display: 'Number of repetitions exceeds limit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_MandatoryElementWithNullValue: Coding = {
  code: 'SYN109',
  display: 'Mandatory element with null value',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_NumberOfAssociationRepetitionsExceedsLimit: Coding = {
  code: 'SYN110',
  display: 'Number of association repetitions exceeds limit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_ValueDoesNotMatchFixedValue: Coding = {
  code: 'SYN111',
  display: 'Value does not match fixed value',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_NumberOfAttributeRepetitionsExceedsLimit: Coding = {
  code: 'SYN112',
  display: 'Number of attribute repetitions exceeds limit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_FormalConstraintViolation: Coding = {
  code: 'SYN113',
  display: 'Formal constraint violation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
const v3_AcknowledgementDetailCode_InsufficientRepetitions: Coding = {
  code: 'SYN114',
  display: 'Insufficient repetitions',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailCode',
};
/**
 *   OpenIssue:
 * Missing description.
 */
export const V3AcknowledgementDetailCode = {
  /**
   * Refelects rejections because elements of the communication are not supported in the current context.
   */
  AcknowledgementDetailNotSupportedCode: v3_AcknowledgementDetailCode_AcknowledgementDetailNotSupportedCode,
  /**
   * An internal software component (database, application, queue mechanism, etc.) has failed, leading to inability to process the message.
   */
  InternalSystemError: v3_AcknowledgementDetailCode_InternalSystemError,
  /**
   * Rejection: The message can't be stored by the receiver due to an unspecified internal application issue. The message was neither processed nor stored by the receiving application.
   */
  NoStorageSpaceForMessage: v3_AcknowledgementDetailCode_NoStorageSpaceForMessage,
  /**
   * The interaction (or: this version of the interaction) is not supported.
   */
  UnsupportedInteraction: v3_AcknowledgementDetailCode_UnsupportedInteraction,
  /**
   * The Processing ID is not supported.
   */
  UnsupportedProcessingId: v3_AcknowledgementDetailCode_UnsupportedProcessingId,
  /**
   * The Version ID is not supported.
   */
  UnsupportedVersionId: v3_AcknowledgementDetailCode_UnsupportedVersionId,
  /**
   * The processing mode is not supported.
   */
  UnsupportedProcessingMode: v3_AcknowledgementDetailCode_UnsupportedProcessingMode,
  /**
   * The Device.id of the sender is unknown.
   */
  UnknownSender: v3_AcknowledgementDetailCode_UnknownSender,
  /**
   * The receiver requires information in the attentionLine classes for routing purposes.
   */
  UnrecognizedAttentionline: v3_AcknowledgementDetailCode_UnrecognizedAttentionline,
  /**
   * Error: The destination of this message is known to the receiving application. Messages have been successfully routed to that destination in the past. The link to the destination application or an intermediate application is unavailable.
   */
  MessageRoutingErrorDestinationUnreachable: v3_AcknowledgementDetailCode_MessageRoutingErrorDestinationUnreachable,
  /**
   * The destination of this message is unknown to the receiving application. The receiving application in the message does not match the application which received the message. The message was neither routed, processed nor stored by the receiving application.
   */
  ErrorMessageRoutingErrorUnknownDestination: v3_AcknowledgementDetailCode_ErrorMessageRoutingErrorUnknownDestination,
  /**
   * Warning: The destination of this message is known to the receiving application. Messages have been successfully routed to that destination in the past. The link to the destination application or an intermediate application is (temporarily) unavailable. The receiving application will forward the message as soon as the destination can be reached again.
   */
  MessageRoutingWarningDestinationUnreachable: v3_AcknowledgementDetailCode_MessageRoutingWarningDestinationUnreachable,
  /**
   * Reflects errors in the syntax or structure of the communication.
   */
  SyntaxError: v3_AcknowledgementDetailCode_SyntaxError,
  /**
   * Required association missing in message; or the sequence of the classes is different than required by the standard or one of the conformance profiles identified in the message.
   */
  RequiredAssociationMissing: v3_AcknowledgementDetailCode_RequiredAssociationMissing,
  /**
   * A required attribute is missing in a class.
   */
  RequiredAttributeMissing: v3_AcknowledgementDetailCode_RequiredAttributeMissing,
  /**
   * The attribute contained data of the wrong data type, e.g. a numeric attribute contained "FOO".
   */
  DataTypeError: v3_AcknowledgementDetailCode_DataTypeError,
  /**
   * An attribute value was compared against the corresponding code system, and no match was found.
   */
  ValueNotFoundInCodeSystem: v3_AcknowledgementDetailCode_ValueNotFoundInCodeSystem,
  /**
   * An attribute value referenced a code system that is not valid for an attribute constrained to CNE.
   */
  InvalidCodeSystemInCNE: v3_AcknowledgementDetailCode_InvalidCodeSystemInCNE,
  /**
   * Description: Required association or attribute missing in message; or the sequence of the classes is different than required by the standard or one of the conformance profiles identified in the message.
   */
  RequiredElementMissing: v3_AcknowledgementDetailCode_RequiredElementMissing,
  /**
   * Description: A coded attribute or datatype property violates one of the terminology constraints specified in the standard or one of the conformance profiles or templates declared by the instance.
   */
  TerminologyError: v3_AcknowledgementDetailCode_TerminologyError,
  /**
   * Description: A coded attribute is referencing a code that has been deprecated by the owning code system.
   */
  DeprecatedCode: v3_AcknowledgementDetailCode_DeprecatedCode,
  /**
   * Description: The number of repetitions of a (group of) association(s) or attribute(s) exceeds the limits of the standard or of one of the conformance profiles or templates identified in the message.
   */
  NumberOfRepetitionsExceedsLimit: v3_AcknowledgementDetailCode_NumberOfRepetitionsExceedsLimit,
  /**
   * Description: An attribute or association identified as mandatory in a specification or declared conformance profile or template has been specified with a null flavor.
   */
  MandatoryElementWithNullValue: v3_AcknowledgementDetailCode_MandatoryElementWithNullValue,
  /**
   * The number of repetitions of a (group of) association(s) exceeds the limits of the standard or of one of the conformance profiles identified in the message.
   */
  NumberOfAssociationRepetitionsExceedsLimit: v3_AcknowledgementDetailCode_NumberOfAssociationRepetitionsExceedsLimit,
  /**
   * Description: The value of an attribute or property differs from the fixed value asserted in the standard or one of the conformance profiles or templates declared in the message.
   */
  ValueDoesNotMatchFixedValue: v3_AcknowledgementDetailCode_ValueDoesNotMatchFixedValue,
  /**
   * The number of repetitions of an attribute exceeds the limits of the standard or of one of the conformance profiles identified in the message.
   */
  NumberOfAttributeRepetitionsExceedsLimit: v3_AcknowledgementDetailCode_NumberOfAttributeRepetitionsExceedsLimit,
  /**
   * Description: A formal constraint asserted in the standard or one of the conformance profiles or templates declared in the message has been violated.
   */
  FormalConstraintViolation: v3_AcknowledgementDetailCode_FormalConstraintViolation,
  /**
   * Description: The number of repetitions of a group of association or attributes is less than the required minimum for the standard or of one of the conformance profiles or templates identified in the message.
   */
  InsufficientRepetitions: v3_AcknowledgementDetailCode_InsufficientRepetitions,
};
const v3_AcknowledgementDetailType_Error: Coding = {
  code: 'E',
  display: 'Error',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailType',
};
const v3_AcknowledgementDetailType_ERR: Coding = {
  code: 'ERR',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailType',
};
const v3_AcknowledgementDetailType_Information: Coding = {
  code: 'I',
  display: 'Information',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailType',
};
const v3_AcknowledgementDetailType_INFO: Coding = {
  code: 'INFO',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailType',
};
const v3_AcknowledgementDetailType_Warning: Coding = {
  code: 'W',
  display: 'Warning',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailType',
};
const v3_AcknowledgementDetailType_WARN: Coding = {
  code: 'WARN',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementDetailType',
};
/**
 *  A code identifying the specific message to be provided.  Discussion:
 * A textual value may be specified as the print name, or for non-coded messages, as the original text.  Examples:
 * 'Required attribute xxx is missing', 'System will be unavailable March 19 from 0100 to 0300'
 */
export const V3AcknowledgementDetailType = {
  /**
   * Definition:An issue which has prevented, or will prevent (unless a management is provided for the issue by the sender), the successful processing of an interaction.  Response interactions which include an issue which is an Error are a 'rejection', indicating that the request was not successfully processed.
   *
   *
   *                            Example:Unable to find specified patient.
   */
  Error: v3_AcknowledgementDetailType_Error,
  ERR: v3_AcknowledgementDetailType_ERR,
  /**
   * Definition: The message relates to an issue which has no bearing on the successful processing of the request.  Information issues cannot be overridden by specifying a management.
   *
   *
   *                            Example: A Patient's coverage will expire in 5 days.
   */
  Information: v3_AcknowledgementDetailType_Information,
  INFO: v3_AcknowledgementDetailType_INFO,
  /**
   * Definition: The message relates to an issue which cannot prevent the successful processing of a request, but which could result in the processing not having the ideal or intended effect.  Managing a warning issue is not required for successful processing, but will suppress the warning from being raised.
   *
   *
   *                            Example:
   *
   *
   *                         Unexpected additional repetitions of phone number have been ignored.
   */
  Warning: v3_AcknowledgementDetailType_Warning,
  WARN: v3_AcknowledgementDetailType_WARN,
};
const v3_AcknowledgementType_ApplicationAcknowledgementAccept: Coding = {
  code: 'AA',
  display: 'Application Acknowledgement Accept',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementType',
};
const v3_AcknowledgementType_ApplicationAcknowledgementError: Coding = {
  code: 'AE',
  display: 'Application Acknowledgement Error',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementType',
};
const v3_AcknowledgementType_ApplicationAcknowledgementReject: Coding = {
  code: 'AR',
  display: 'Application Acknowledgement Reject',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementType',
};
const v3_AcknowledgementType_AcceptAcknowledgementCommitAccept: Coding = {
  code: 'CA',
  display: 'Accept Acknowledgement Commit Accept',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementType',
};
const v3_AcknowledgementType_AcceptAcknowledgementCommitError: Coding = {
  code: 'CE',
  display: 'Accept Acknowledgement Commit Error',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementType',
};
const v3_AcknowledgementType_AcceptAcknowledgementCommitReject: Coding = {
  code: 'CR',
  display: 'Accept Acknowledgement Commit Reject',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AcknowledgementType',
};
/**
 *  This attribute contains an acknowledgement code as described in the HL7 message processing rules.  OpenIssue:
 * Description was copied from attribute and needs to be improved to be appropriate for a code system.
 */
export const V3AcknowledgementType = {
  /**
   * Receiving application successfully processed message.
   */
  ApplicationAcknowledgementAccept: v3_AcknowledgementType_ApplicationAcknowledgementAccept,
  /**
   * Receiving application found error in processing message.  Sending error response with additional error detail information.
   */
  ApplicationAcknowledgementError: v3_AcknowledgementType_ApplicationAcknowledgementError,
  /**
   * Receiving application failed to process message for reason unrelated to content or format.  Original message sender must decide on whether to automatically send message again.
   */
  ApplicationAcknowledgementReject: v3_AcknowledgementType_ApplicationAcknowledgementReject,
  /**
   * Receiving message handling service accepts responsibility for passing message onto receiving application.
   */
  AcceptAcknowledgementCommitAccept: v3_AcknowledgementType_AcceptAcknowledgementCommitAccept,
  /**
   * Receiving message handling service cannot accept message for any other reason (e.g. message sequence number, etc.).
   */
  AcceptAcknowledgementCommitError: v3_AcknowledgementType_AcceptAcknowledgementCommitError,
  /**
   * Receiving message handling service rejects message if interaction identifier, version or processing mode is incompatible with known receiving application role information.
   */
  AcceptAcknowledgementCommitReject: v3_AcknowledgementType_AcceptAcknowledgementCommitReject,
};
const v3_ActClass_ActClassContainer: Coding = {
  code: '_ActClassContainer',
  display: 'ActClassContainer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_RecordOrganizer: Coding = {
  code: '_ActClassRecordOrganizer',
  display: 'record organizer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_ActClassROI: Coding = {
  code: '_ActClassROI',
  display: 'ActClassROI',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_SubjectPhysicalPosition: Coding = {
  code: '_SubjectPhysicalPosition',
  display: 'subject physical position',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Accommodation: Coding = {
  code: 'ACCM',
  display: 'accommodation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Account: Coding = {
  code: 'ACCT',
  display: 'account',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Accession: Coding = {
  code: 'ACSN',
  display: 'accession',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Act: Coding = {
  code: 'ACT',
  display: 'act',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Action: Coding = {
  code: 'ACTN',
  display: 'action',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_FinancialAdjudication: Coding = {
  code: 'ADJUD',
  display: 'financial adjudication',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_AcquisitionExposure: Coding = {
  code: 'AEXPOS',
  display: 'acquisition exposure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_DetectedIssue: Coding = {
  code: 'ALRT',
  display: 'detected issue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Battery: Coding = {
  code: 'BATTERY',
  display: 'battery',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_ControlAct: Coding = {
  code: 'CACT',
  display: 'control act',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Category: Coding = {
  code: 'CATEGORY',
  display: 'category',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_CDALevelOneClinicalDocument: Coding = {
  code: 'CDALVLONE',
  display: 'CDA Level One clinical document',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_ClinicalTrial: Coding = {
  code: 'CLNTRL',
  display: 'clinical trial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Cluster: Coding = {
  code: 'CLUSTER',
  display: 'Cluster',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Contract: Coding = {
  code: 'CNTRCT',
  display: 'contract',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Composition: Coding = {
  code: 'COMPOSITION',
  display: 'composition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Concern: Coding = {
  code: 'CONC',
  display: 'concern',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Consent: Coding = {
  code: 'CONS',
  display: 'consent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_RecordContainer: Coding = {
  code: 'CONTAINER',
  display: 'record container',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_ContainerRegistration: Coding = {
  code: 'CONTREG',
  display: 'container registration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Coverage: Coding = {
  code: 'COV',
  display: 'coverage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_ClinicalTrialTimepointEvent: Coding = {
  code: 'CTTEVENT',
  display: 'clinical trial timepoint event',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_DeterminantPeptide: Coding = {
  code: 'DETPOL',
  display: 'determinant peptide',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_DiagnosticImage: Coding = {
  code: 'DGIMG',
  display: 'diagnostic image',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_DisciplinaryAction: Coding = {
  code: 'DISPACT',
  display: 'disciplinary action',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Document: Coding = {
  code: 'DOC',
  display: 'document',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_DocumentBody: Coding = {
  code: 'DOCBODY',
  display: 'document body',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_ClinicalDocument: Coding = {
  code: 'DOCCLIN',
  display: 'clinical document',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_DOCCNTNT: Coding = {
  code: 'DOCCNTNT',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_DOCLIST: Coding = {
  code: 'DOCLIST',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_DOCLSTITM: Coding = {
  code: 'DOCLSTITM',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_DOCPARA: Coding = {
  code: 'DOCPARA',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_DocumentSection: Coding = {
  code: 'DOCSECT',
  display: 'document section',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_DOCTBL: Coding = {
  code: 'DOCTBL',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_ElectronicHealthRecord: Coding = {
  code: 'EHR',
  display: 'electronic health record',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Encounter: Coding = {
  code: 'ENC',
  display: 'encounter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Entry: Coding = {
  code: 'ENTRY',
  display: 'entry',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_ExpressionLevel: Coding = {
  code: 'EXP',
  display: 'expression level',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Exposure: Coding = {
  code: 'EXPOS',
  display: 'exposure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Extract: Coding = {
  code: 'EXTRACT',
  display: 'extract',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_FinancialContract: Coding = {
  code: 'FCNTRCT',
  display: 'financial contract',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Folder: Coding = {
  code: 'FOLDER',
  display: 'folder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_GenomicObservation: Coding = {
  code: 'GEN',
  display: 'genomic observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Grouper: Coding = {
  code: 'GROUPER',
  display: 'grouper',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_PublicHealthCase: Coding = {
  code: 'HCASE',
  display: 'public health case',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Incident: Coding = {
  code: 'INC',
  display: 'incident',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Information: Coding = {
  code: 'INFO',
  display: 'information',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Inform: Coding = {
  code: 'INFRM',
  display: 'inform',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_InvoiceElement: Coding = {
  code: 'INVE',
  display: 'invoice element',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Investigation: Coding = {
  code: 'INVSTG',
  display: 'investigation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_JurisdictionalPolicy: Coding = {
  code: 'JURISPOL',
  display: 'jurisdictional policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_LINKHTML: Coding = {
  code: 'LINKHTML',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_WorkingList: Coding = {
  code: 'LIST',
  display: 'working list',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Locus: Coding = {
  code: 'LOC',
  display: 'locus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_LOCALATTR: Coding = {
  code: 'LOCALATTR',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_LOCALMRKP: Coding = {
  code: 'LOCALMRKP',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_MonitoringProgram: Coding = {
  code: 'MPROT',
  display: 'monitoring program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Observation: Coding = {
  code: 'OBS',
  display: 'observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_CorrelatedObservationSequences: Coding = {
  code: 'OBSCOR',
  display: 'correlated observation sequences',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_ObservationSeries: Coding = {
  code: 'OBSSER',
  display: 'observation series',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Ordered: Coding = {
  code: 'ordered',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Organizer: Coding = {
  code: 'ORGANIZER',
  display: 'organizer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_OrganizationalPolicy: Coding = {
  code: 'ORGPOL',
  display: 'organizational policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Outbreak: Coding = {
  code: 'OUTBR',
  display: 'outbreak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_CareProvision: Coding = {
  code: 'PCPR',
  display: 'care provision',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Phenotype: Coding = {
  code: 'PHN',
  display: 'phenotype',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Polypeptide: Coding = {
  code: 'POL',
  display: 'polypeptide',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Policy: Coding = {
  code: 'POLICY',
  display: 'policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Position: Coding = {
  code: 'POS',
  display: 'position',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_PositionAccuracy: Coding = {
  code: 'POSACC',
  display: 'position accuracy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_PositionCoordinate: Coding = {
  code: 'POSCOORD',
  display: 'position coordinate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Procedure: Coding = {
  code: 'PROC',
  display: 'procedure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_REFR: Coding = {
  code: 'REFR',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Registration: Coding = {
  code: 'REG',
  display: 'registration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Review: Coding = {
  code: 'REV',
  display: 'review',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_BoundedROI: Coding = {
  code: 'ROIBND',
  display: 'bounded ROI',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_OverlayROI: Coding = {
  code: 'ROIOVL',
  display: 'overlay ROI',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_SubstanceAdministration: Coding = {
  code: 'SBADM',
  display: 'substance administration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_SubstanceExtraction: Coding = {
  code: 'SBEXT',
  display: 'Substance Extraction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_ScopeOfPracticePolicy: Coding = {
  code: 'SCOPOL',
  display: 'scope of practice policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_BioSequence: Coding = {
  code: 'SEQ',
  display: 'bio sequence',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_BioSequenceVariation: Coding = {
  code: 'SEQVAR',
  display: 'bio sequence variation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_SpecimenObservation: Coding = {
  code: 'SPCOBS',
  display: 'specimen observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_SpecimenTreatment: Coding = {
  code: 'SPCTRT',
  display: 'specimen treatment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_SpecimenCollection: Coding = {
  code: 'SPECCOLLECT',
  display: 'Specimen Collection',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Supply: Coding = {
  code: 'SPLY',
  display: 'supply',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_StateTransitionControl: Coding = {
  code: 'STC',
  display: 'state transition control',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_StandardOfPracticePolicy: Coding = {
  code: 'STDPOL',
  display: 'standard of practice policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Storage: Coding = {
  code: 'STORE',
  display: 'storage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Substitution: Coding = {
  code: 'SUBST',
  display: 'Substitution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_TBLCOL: Coding = {
  code: 'TBLCOL',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_TBLCOLGP: Coding = {
  code: 'TBLCOLGP',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_TBLDATA: Coding = {
  code: 'TBLDATA',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_TBLHDR: Coding = {
  code: 'TBLHDR',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_TBLROW: Coding = {
  code: 'TBLROW',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Tbody: Coding = {
  code: 'tbody',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_TransmissionExposure: Coding = {
  code: 'TEXPOS',
  display: 'transmission exposure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Tfoot: Coding = {
  code: 'tfoot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Thead: Coding = {
  code: 'thead',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Topic: Coding = {
  code: 'TOPIC',
  display: 'topic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Transfer: Coding = {
  code: 'TRFR',
  display: 'transfer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Transportation: Coding = {
  code: 'TRNS',
  display: 'transportation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Unordered: Coding = {
  code: 'unordered',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_Verification: Coding = {
  code: 'VERIF',
  display: 'Verification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
const v3_ActClass_FinancialTransaction: Coding = {
  code: 'XACT',
  display: 'financial transaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActClass',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3ActClass = {
  /**
   * ActClassContainer
   */
  ActClassContainer: v3_ActClass_ActClassContainer,
  /**
   * Used to group a set of acts sharing a common context. Organizer structures can nest within other context structures - such as where a document is contained within a folder, or a folder is contained within an EHR extract.
   */
  RecordOrganizer: v3_ActClass_RecordOrganizer,
  /**
   * Regions of Interest (ROI) within a subject Act. Primarily used for making secondary observations on a subset of a subject observation. The relationship between a ROI and its referenced Act is specified through an ActRelationship of type "subject" (SUBJ), which must always be present.
   */
  ActClassROI: v3_ActClass_ActClassROI,
  /**
   * The spatial relationship of a subject whether human, other animal, or plant, to a frame of reference such as gravity or a collection device.
   */
  SubjectPhysicalPosition: v3_ActClass_SubjectPhysicalPosition,
  /**
   * An accommodation is a service provided for a Person or other LivingSubject in which a place is provided for the subject to reside for a period of time.  Commonly used to track the provision of ward, private and semi-private accommodations for a patient.
   */
  Accommodation: v3_ActClass_Accommodation,
  /**
   * A financial account established to track the net result of financial acts.
   */
  Account: v3_ActClass_Account,
  /**
   * A unit of work, a grouper of work items as defined by the system performing that work. Typically some laboratory order fulfillers communicate references to accessions in their communications regarding laboratory orders. Often one or more specimens are related to an accession such that in some environments the accession number is taken as an identifier for a specimen (group).
   */
  Accession: v3_ActClass_Accession,
  /**
   * A record of something that is being done, has been done, can be done, or is intended or requested to be done.
   *
   *
   *                            Examples:The kinds of acts that are common in health care are (1) a clinical observation, (2) an assessment of health condition (such as problems and diagnoses), (3) healthcare goals, (4) treatment services (such as medication, surgery, physical and psychological therapy), (5) assisting, monitoring or attending, (6) training and education services to patients and their next of kin, (7) and notary services (such as advanced directives or living will), (8)  editing and maintaining documents, and many others.
   *
   *
   *                            Discussion and Rationale: Acts are the pivot of the RIM; all domain information and processes are represented primarily in Acts. Any profession or business, including healthcare, is primarily constituted of intentional and occasionally non-intentional actions, performed and recorded by responsible actors. An Act-instance is a record of such an action.
   *
   *                         Acts connect to Entities in their Roles through Participations and connect to other Acts through ActRelationships. Participations are the authors, performers and other responsible parties as well as subjects and beneficiaries (which includes tools and material used in the performance of the act, which are also subjects). The moodCode distinguishes between Acts that are meant as factual records, vs. records of intended or ordered services, and the other modalities in which act can appear.
   *
   *                         One of the Participations that all acts have (at least implicitly) is a primary author, who is responsible of the Act and who "owns" the act. Responsibility for the act means responsibility for what is being stated in the Act and as what it is stated. Ownership of the act is assumed in the sense of who may operationally modify the same act. Ownership and responsibility of the Act is not the same as ownership or responsibility of what the Act-object refers to in the real world. The same real world activity can be described by two people, each being the author of their Act, describing the same real world activity. Yet one can be a witness while the other can be a principal performer. The performer has responsibilities for the physical actions; the witness only has responsibility for making a true statement to the best of his or her ability. The two Act-instances may even disagree, but because each is properly attributed to its author, such disagreements can exist side by side and left to arbitration by a recipient of these Act-instances.
   *
   *                         In this sense, an Act-instance represents a "statement" according to Rector and Nowlan (1991) [Foundations for an electronic medical record. Methods Inf Med. 30.]  Rector and Nowlan have emphasized the importance of understanding the medical record not as a collection of facts, but "a faithful record of what clinicians have heard, seen, thought, and done." Rector and Nowlan go on saying that "the other requirements for a medical record, e.g., that it be attributable and permanent, follow naturally from this view." Indeed the Act class is this attributable statement, and the rules of updating acts (discussed in the state-transition model, see Act.statusCode) versus generating new Act-instances are designed according to this principle of permanent attributable statements.
   *
   *                         Rector and Nolan focus on the electronic medical record as a collection of statements, while attributed statements, these are still mostly factual statements. However, the Act class goes beyond this limitation to attributed factual statements, representing what is known as "speech-acts" in linguistics and philosophy.  The notion of speech-act includes that there is pragmatic meaning in language utterances, aside from just factual statements; and that these utterances interact with the real world to change the state of affairs, even directly cause physical activities to happen. For example, an order is a speech act that (provided it is issued adequately) will cause the ordered action to be physically performed. The speech act theory has culminated in the seminal work by Austin (1962) [How to do things with words. Oxford University Press].
   *
   *                         An activity in the real world may progress from defined, through planned and ordered to executed, which is represented as the mood of the Act. Even though one might think of a single activity as progressing from planned to executed, this progression is reflected by multiple Act-instances, each having one and only one mood that will not change along the Act-instance life cycle.  This is because the attribution and content of speech acts along this progression of an activity may be different, and it is often critical that a permanent and faithful record be maintained of this progression. The specification of orders or promises or plans must not be overwritten by the specification of what was actually done, so as to allow comparing actions with their earlier specifications. Act-instances that describe this progression of the same real world activity are linked through the ActRelationships (of the relationship category "sequel").
   *
   *                         Act as statements or speech-acts are the only representation of real world facts or processes in the HL7 RIM. The truth about the real world is constructed through a combination (and arbitration) of such attributed statements only, and there is no class in the RIM whose objects represent "objective state of affairs" or "real processes" independent from attributed statements. As such, there is no distinction between an activity and its documentation. Every Act includes both to varying degrees. For example, a factual statement made about recent (but past) activities, authored (and signed) by the performer of such activities, is commonly known as a procedure report or original documentation (e.g., surgical procedure report, clinic note etc.). Conversely, a status update on an activity that is presently in progress, authored by the performer (or a close observer) is considered to capture that activity (and is later superceded by a full procedure report). However, both status update and procedure report are acts of the same kind, only distinguished by mood and state (see statusCode) and completeness of the information.
   */
  Act: v3_ActClass_Act,
  /**
   * Sender asks addressee to do something depending on the focal Act of the payload.  An example is "fulfill this order".  Addressee has responsibilities to either reject the message or to act on it in an appropriate way (specified by the specific receiver responsibilities for the interaction).
   */
  Action: v3_ActClass_Action,
  /**
   * A transformation process where a requested invoice is transformed into an agreed invoice.  Represents the adjudication processing of an invoice (claim).  Adjudication results can be adjudicated as submitted, with adjustments or refused.
   *
   *                         Adjudication results comprise 2 components: the adjudication processing results and a restated (or adjudicated) invoice or claim
   */
  FinancialAdjudication: v3_ActClass_FinancialAdjudication,
  /**
   * Description:
   *
   *
   *                         An acquisition exposure act describes the proximity (location and time) through which the participating entity was potentially exposed to a physical (including energy), chemical or biological agent from another entity.  The acquisition exposure act is used in conjunction with transmission exposure acts as part of an analysis technique for contact tracing.  Although an exposure can be decomposed into transmission and acquisition exposures, there is no requirement that all exposures be treated in this fashion.
   *
   *
   *                            Constraints:  The Acquisition Exposure inherits the participation constraints that apply to Exposure with the following exception.  The EXPSRC (exposure source) participation must never be associated with the Transmission Exposure either directly or via context conduction.
   */
  AcquisitionExposure: v3_ActClass_AcquisitionExposure,
  /**
   * An observation identifying a potential adverse outcome as a result of an Act or combination of Acts.
   *
   *
   *                            Examples: Detection of a drug-drug interaction; Identification of a late-submission for an invoice; Requesting discharge for a patient who does not meet hospital-defined discharge criteria.
   *
   *
   *                            Discussion: This class is commonly used for identifying 'business rule' or 'process' problems that may result in a refusal to carry out a particular request. In some circumstances it may be possible to 'bypass' a problem by modifying the request to acknowledge the issue and/or by providing some form of mitigation.
   *
   *
   *                            Constraints: the Act or Acts that may cause the the adverse outcome are the target of a subject ActRelationship. The subbtypes of this concept indicate the type of problem being detected (e.g. drug-drug interaction) while the Observation.value is used to repesent a specific problem code (e.g. specific drug-drug interaction id).
   */
  DetectedIssue: v3_ActClass_DetectedIssue,
  /**
   * Definition: An observation that is composed of a set of observations. These observations typically have a logical or practical grouping for generally accepted clinical or functional purposes, such as observations that are run together because of automation. A battery can define required and optional component observations and, in some cases, will define complex rules that determine whether or not a particular observation is made. BATTERY is a constraint on the Observation class in that it is understood to always be composed of component observations.
   *
   *
   *                            UsageNotes: The focus in a BATTERY is that it is composed of individual observations. In request (RQO) mood, a battery is a request to perform the component observations. In event (EVN) mood a battery is a reporting of associated set of observation events. In definition mood a battery is the definition of the associated set of observations.
   *
   *
   *                            Examples: Vital signs, Full blood count, Chemistry panel.
   */
  Battery: v3_ActClass_Battery,
  /**
   * An act representing a system action such as the change of state of another act or the initiation of a query.  All control acts represent trigger events in the HL7 context.  ControlActs may occur in different moods.
   */
  ControlAct: v3_ActClass_ControlAct,
  /**
   * A group of entries within a composition or topic that have a common characteristic - for example, Examination, Diagnosis, Management OR Subjective, Objective, Analysis, Plan.
   *
   *                         The distinction from Topic relates to value sets. For Category there is a bounded list of things like "Examination", "Diagnosis" or SOAP categories. For Topic the list is wide open to any clinical condition or reason for a part of an encounter.
   *
   *                         A CATEGORY MAY CONTAIN ENTRIES.
   */
  Category: v3_ActClass_Category,
  /**
   * A clinical document that conforms to Level One of the HL7 Clinical Document Architecture (CDA)
   */
  CDALevelOneClinicalDocument: v3_ActClass_CDALevelOneClinicalDocument,
  /**
   * The set of actions that define an experiment to assess the effectiveness and/or safety of a biopharmaceutical product (food, drug, device, etc.).  In definition mood, this set of actions is often embodied in a clinical trial protocol; in event mood, this designates the aggregate act of applying the actions to one or more subjects.
   */
  ClinicalTrial: v3_ActClass_ClinicalTrial,
  /**
   * Description:An ACT that organizes a set of component acts into a semantic grouping that have a shared subject. The subject may be either a subject participation (SBJ), subject act relationship (SUBJ), or child participation/act relationship types.
   *
   *
   *                            Discussion: The focus in a CLUSTER act is the grouping of the contained acts.  For example "a request to cluster" (RQO), "a type of cluster that is allowed to occur" (DEF), etc.
   *
   *
   *                            Examples:
   *
   *
   *
   *
   *                               Radiologic investigations that might include administration of a dye, followed by radiographic observations;
   *
   *
   *
   *                               "Isolate cluster" which includes all testing and specimen processing performed on a specific isolate;
   *
   *
   *
   *                               a set of actions to perform at a particular stage in a clinical trial.
   */
  Cluster: v3_ActClass_Cluster,
  /**
   * An agreement of obligation between two or more parties that is subject to contractual law and enforcement.
   */
  Contract: v3_ActClass_Contract,
  /**
   * A context representing a grouped commitment of information to the EHR. It is considered the unit of modification of the record, the unit of transmission in record extracts, and the unit of attestation by authorizing clinicians.
   *
   *                         A composition represents part of a patient record originating from a single interaction between an authenticator and the record.
   *
   *                         Unless otherwise stated all statements within a composition have the same authenticator, apply to the same patient and were recorded in a single session of use of a single application.
   *
   *                         A composition contains organizers and entries.
   */
  Composition: v3_ActClass_Composition,
  /**
   * Definition: A worry that tends to persist over time and has as its subject a state or process. The subject of the worry has the potential to require intervention or management.
   *
   *
   *                            Examples: an observation result, procedure, substance administration, equipment repair status, device recall status, a health risk, a financial risk, public health risk, pregnancy, health maintenance, allergy, and acute or chronic illness.
   */
  Concern: v3_ActClass_Concern,
  /**
   * The Consent class represents informed consents and all similar medico-legal transactions between the patient (or his legal guardian) and the provider. Examples are informed consent for surgical procedures, informed consent for clinical trials, advanced beneficiary notice, against medical advice decline from service, release of information agreement, etc.
   *
   *                         The details of consents vary. Often an institution has a number of different consent forms for various purposes, including reminding the physician about the topics to mention. Such forms also include patient education material. In electronic medical record communication, consents thus are information-generating acts on their own and need to be managed similar to medical activities. Thus, Consent is modeled as a special class of Act.
   *
   *                         The "signatures" to the consent document are represented electronically through Participation instances to the consent object. Typically an informed consent has Participation.typeCode of "performer", the healthcare provider informing the patient, and "consenter", the patient or legal guardian. Some consent may associate a witness or a notary public (e.g., living wills, advanced directives). In consents where a healthcare provider is not required (e.g. living will), the performer may be the patient himself or a notary public.
   *
   *                         Some consent has a minimum required delay between the consent and the service, so as to allow the patient to rethink his decisions. This minimum delay can be expressed in the act definition by the ActRelationship.pauseQuantity attribute that delays the service until the pause time has elapsed after the consent has been completed.
   */
  Consent: v3_ActClass_Consent,
  /**
   * Description: Container of clinical statements. Navigational. No semantic content. Knowledge of the section code is not required to interpret contained observations. Represents a heading in a heading structure, or "container tree".
   *
   *                         The record entries relating to a single clinical session are usually grouped under headings that represent phases of the encounter, or assist with layout and navigation. Clinical headings usually reflect the clinical workflow during a care session, and might also reflect the main author's reasoning processes. Much research has demonstrated that headings are used differently by different professional groups and specialties, and that headings are not used consistently enough to support safe automatic processing of the E H R.
   */
  RecordContainer: v3_ActClass_RecordContainer,
  /**
   * An Act where a container is registered either via an automated sensor, such as a barcode reader,  or by manual receipt
   */
  ContainerRegistration: v3_ActClass_ContainerRegistration,
  /**
   * When used in the EVN mood, this concept means with respect to a covered party:
   *
   *
   *
   *                               A health care insurance policy or plan that is contractually binding between two or more parties; or
   *
   *
   *
   *                               A health care program, usually administered by government entities, that provides coverage to persons determined eligible under the terms of the program.
   *
   *
   *
   *
   *
   *                               When used in the definition (DEF) mood, COV means potential coverage for a patient who may or may not be a covered party.
   *
   *
   *
   *                               The concept's meaning is fully specified by the choice of ActCoverageTypeCode (abstract) ActProgramCode or ActInsurancePolicyCode.
   */
  Coverage: v3_ActClass_Coverage,
  /**
   * An identified point during a clinical trial at which one or more actions are scheduled to be performed (definition mood), or are actually performed (event mood).  The actions may or may not involve an encounter between the subject and a healthcare professional.
   */
  ClinicalTrialTimepointEvent: v3_ActClass_ClinicalTrialTimepointEvent,
  /**
   * Description:A determinant peptide in a polypeptide as described by polypeptide.
   */
  DeterminantPeptide: v3_ActClass_DeterminantPeptide,
  /**
   * Class for holding attributes unique to diagnostic images.
   */
  DiagnosticImage: v3_ActClass_DiagnosticImage,
  /**
   * An action taken with respect to a subject Entity by a regulatory or authoritative body with supervisory capacity over that entity. The action is taken in response to behavior by the subject Entity that body finds to be 							undesirable.
   *
   *                         Suspension, license restrictions, monetary fine, letter of reprimand, mandated training, mandated supervision, etc.Examples:
   */
  DisciplinaryAction: v3_ActClass_DisciplinaryAction,
  /**
   * The notion of a document comes particularly from the paper world, where it corresponds to the contents recorded on discrete pieces of paper. In the electronic world, a document is a kind of composition that bears resemblance to their paper world counter-parts. Documents typically are meant to be human-readable.
   *
   *                         HL7's notion of document differs from that described in the W3C XML Recommendation, in which a document refers specifically to the contents that fall between the root element's start-tag and end-tag. Not all XML documents are HL7 documents.
   */
  Document: v3_ActClass_Document,
  /**
   * A context that distinguishes the body of a document from the document header. This is seen, for instance, in HTML documents, which have discrete <head> and <body> elements.
   */
  DocumentBody: v3_ActClass_DocumentBody,
  /**
   * A clinical document is a documentation of clinical observations and services, with the following characteristics:
   *
   *
   *
   *                               Persistence - A clinical document continues to exist in an unaltered state, for a time period defined by local and regulatory requirements;
   *
   *
   *
   *                               Stewardship - A clinical document is maintained by a person or organization entrusted with its care;
   *
   *
   *
   *                               Potential for authentication - A clinical document is an assemblage of information that is intended to be legally authenticated;
   *
   *
   *
   *                               Wholeness - Authentication of a clinical document applies to the whole and does not apply to portions of the document without the full context of the document;
   *
   *
   *
   *                               Human readability - A clinical document is human readable.
   */
  ClinicalDocument: v3_ActClass_ClinicalDocument,
  DOCCNTNT: v3_ActClass_DOCCNTNT,
  DOCLIST: v3_ActClass_DOCLIST,
  DOCLSTITM: v3_ActClass_DOCLSTITM,
  DOCPARA: v3_ActClass_DOCPARA,
  /**
   * A context that subdivides the body of a document. Document sections are typically used for human navigation, to give a reader a clue as to the expected content. Document sections are used to organize and provide consistency to the contents of a document body. Document sections can contain document sections and can contain entries.
   */
  DocumentSection: v3_ActClass_DocumentSection,
  DOCTBL: v3_ActClass_DOCTBL,
  /**
   * A context that comprises all compositions. The EHR is an extract that includes the entire chart.
   *
   *
   *                            NOTE: In an exchange scenario, an EHR is a specialization of an extract.
   */
  ElectronicHealthRecord: v3_ActClass_ElectronicHealthRecord,
  /**
   * An interaction between a patient and healthcare participant(s) for the purpose of providing patient service(s) or assessing the health status of a patient.  For example, outpatient visit to multiple departments, home health support (including physical therapy), inpatient hospital stay, emergency room visit, field visit (e.g., traffic accident), office visit, occupational therapy, telephone call.
   */
  Encounter: v3_ActClass_Encounter,
  /**
   * This context represents the information acquired and recorded for an observation, a clinical statement such as a portion of the patient's history or an inference or assertion, or an action that might be intended or has actually been performed. This class may represent both the actual data describing the observation, inference, or action, and optionally the details supporting the clinical reasoning process such as a reference to an electronic guideline, decision support system, or other knowledge reference.
   */
  Entry: v3_ActClass_Entry,
  /**
   * Description:An expression level of genes/proteins or other expressed genomic entities.
   */
  ExpressionLevel: v3_ActClass_ExpressionLevel,
  /**
   * An interaction between entities that provides opportunity for transmission of a physical, chemical, or biological agent from an exposure source entity to an exposure target entity.
   *
   *
   *                            Examples:  The following examples are provided to indicate what interactions are considered exposures rather than other types of Acts:
   *
   *
   *
   *                               A patient accidentally receives three times the recommended dose of their medication due to a dosing error.
   *
   *
   *
   *                                     This is a substance administration.  Public health and/or safety authorities may also be interested in documenting this with an associated exposure.
   *
   *
   *
   *
   *
   *                               A patient accidentally is dispensed an incorrect medicine (e.g., clomiphene instead of clomipramine).  They have taken several doses before the mistake is detected.  They are therefore "exposed" to a medicine that there was no therapeutic indication for them to receive.
   *
   *
   *
   *                                     There are several substance administrations in this example.  Public health and/or safety authorities may also be interested in documenting this with associated exposures.
   *
   *
   *
   *
   *
   *                               In a busy medical ward, a patient is receiving chemotherapy for a lymphoma.  Unfortunately, the IV infusion bag containing the medicine splits, spraying cytotoxic medication over the patient being treated and the patient in the adjacent bed.
   *
   *
   *
   *                                     There are three substance administrations in this example.  The first is the intended one (IV infusion) with its associated (implicit) exposure.  There is an incident with an associated substance administration to the same patient involving the medication sprayed over the patient as well as an associated exposure.  Additionally, the incident includes a substance administration involving the spraying of medication on the adjacent patient, also with an associated exposure.
   *
   *
   *
   *
   *
   *                               A patient who is a refugee from a war-torn African nation arrives in a busy inner city A&E department suffering from a cough with bloody sputum.  Not understanding the registration and triage process, they sit in the waiting room for several hours before it is noticed that they have not booked in.  As soon as they are being processed, it is suspected that they are suffering from TB.  Vulnerable (immunosuppressed) patients who were sharing the waiting room with this patient may have been exposed to the tubercule bacillus, and must be traced for investigation.
   *
   *
   *
   *                                     This is an exposure (or possibly multiple exposures) in the waiting room involving the refugee and everyone else in the waiting room during the period.  There might also be a number of known or presumed substance administrations (coughing) via several possible routes.  The substance administrations are only hypotheses until confirmed by further testing.
   *
   *
   *
   *
   *
   *                               A patient who has received an elective total hip replacement procedure suffers a prolonged stay in hospital, due to contracting an MRSA infection in the surgical wound site after the surgery.
   *
   *
   *
   *                                     This is an exposure to MRSA.  Although there was some sort of substance administration, it's possible the exact mechanism for introduction of the MRSA into the wound will not be identified.
   *
   *
   *
   *
   *
   *                               Routine maintenance of the X-ray machines at a local hospital reveals a serious breach of the shielding on one of the machines.  Patients who have undergone investigations using that machine in the last month are likely to have been exposed to significantly higher doses of X-rays than was intended, and must be tracked for possible adverse effects.
   *
   *
   *
   *                                     There has been an exposure of each patient who used the machine in the past 30 days. Some patients may have had substance administrations.
   *
   *
   *
   *
   *
   *                               A new member of staff is employed in the laundry processing room of a small cottage hospital, and a misreading of the instructions for adding detergents results in fifty times the usual concentration of cleaning materials being added to a batch of hospital bedding.  As a result, several patients have been exposed to very high levels of detergents still present in the "clean" bedding, and have experienced dermatological reactions to this.
   *
   *
   *
   *                                     There has been an incident with multiple exposures to several patients.  Although there are substance administrations involving the application of the detergent to the skin of the patients, it is expected that the substance administrations would not be directly documented.
   *
   *
   *
   *
   *
   *                               Seven patients who are residents in a health care facility for the elderly mentally ill have developed respiratory problems. After several months of various tests having been performed and various medications prescribed to these patients, the problem is traced to their being "sensitive" to a new fungicide used in the wall plaster of the ward where these patients reside.
   *
   *
   *
   *                                     The patients have been continuously exposed to the fungicide.  Although there have been continuous substance administrations (via breathing) this would not normally be documented as a substance administration.
   *
   *
   *
   *
   *
   *                               A patient with osteoarthritis of the knees is treated symptomatically using analgesia, paracetamol (acetaminophen) 1g up to four times a day for pain relief.  His GP does not realize that the patient has, 20 years previously (while at college) had severe alcohol addiction problems, and now, although this is completely under control, his liver has suffered significantly, leaving him more sensitive to hepatic toxicity from paracetamol use.  Later that year, the patient returns with a noticeable level of jaundice.  Paracetamol is immediately withdrawn and alternative solutions for the knee pain are sought.  The jaundice gradually subsides with conservative management, but referral to the gastroenterologist is required for advice and monitoring.
   *
   *
   *
   *                                     There is a substance administration with an associated exposure.  The exposure component is based on the relative toxic level of the substance to a patient with a compromised liver function.
   *
   *
   *
   *
   *
   *                               A patient goes to their GP complaining of abdominal pain, having been discharged from the local hospital ten days' previously after an emergency appendectomy.  The GP can find nothing particularly amiss, and presumes it is post operative surgical pain that will resolve.  The patient returns a fortnight later, when the GP prescribes further analgesia, but does decide to request an outpatient surgical follow-up appointment.  At this post-surgical outpatient review, the registrar decides to order an ultrasound, which, when performed three weeks later, shows a small faint inexplicable mass.  A laparoscopy is then performed, as a day case procedure, and a piece of a surgical swab is removed from the patient's abdominal cavity.  Thankfully, a full recovery then takes place.
   *
   *
   *
   *                                     This is a procedural sequelae.  There may be an Incident recorded for this also.
   *
   *
   *
   *
   *
   *                               A patient is slightly late for a regular pacemaker battery check in the Cardiology department of the local hospital.  They are hurrying down the second floor corridor.  A sudden summer squall has recently passed over the area, and rain has come in through an open corridor window leaving a small puddle on the corridor floor.  In their haste, the patient slips in the puddle and falls so badly that they have to be taken to the A&E department, where it is discovered on investigation they have slightly torn the cruciate ligament in their left knee.
   *
   *
   *
   *                                     This is not an exposure.  There has been an incident.
   *
   *
   *
   *
   *
   *
   *                            Usage Notes: This class deals only with opportunity and not the outcome of the exposure; i.e. not all exposed parties will necessarily experience actual harm or benefit.
   *
   *                         Exposure differs from Substance Administration by the absence of the participation of a performer in the act.
   *
   *                         The following participations SHOULD be used with the following participations to distinguish the specific entities:
   *
   *
   *
   *                               The exposed entity participates via the "exposure target" (EXPTRGT) participation.
   *
   *
   *
   *                               An entity that has carried the agent transmitted in the exposure participates via the "exposure source" (EXSRC) participation.  For example:
   *
   *
   *
   *                                     a person or animal who carried an infectious disease and interacts (EXSRC) with another person or animal (EXPTRGT) transmitting the disease agent;
   *
   *
   *
   *                                     a place or other environment (EXSRC) and a person or animal (EXPTRGT) who is exposed in the presence of this environment.
   *
   *
   *
   *
   *
   *                               When it is unknown whether a participating entity is the source of the agent (EXSRC) or the target of the transmission (EXPTRGT), the "exposure participant" (EXPART) is used.
   *
   *
   *
   *                               The physical (including energy), chemical or biological substance which is participating in the exposure uses the "exposure agent" (EXPAGNT) participation.  There are at least three scenarios:
   *
   *
   *
   *                                     the player of the Role that participates as EXPAGNT is the chemical or biological substance mixed or carried by the scoper-entity of the Role (e.g., ingredient role); or
   *
   *
   *
   *                                     the player of the Role that participates as EXPAGNT is a mixture known to contain the chemical, radiological or biological substance of interest; or
   *
   *
   *
   *                                     the player of the Role that participates as a EXPAGNT is known to carry the agent (i.e., the player is a fomite, vector, etc.).
   *
   *
   *
   *
   *
   *                         The Exposure.statusCode attribute should be interpreted as the state of the Exposure business object (e.g., active, aborted, completed) and not the clinical status of the exposure (e.g., probable, confirmed).  The clinical status of the exposure should be associated with the exposure via a subject observation.
   *
   *
   *                            Design Comment: The usage notes require a clear criterion for determining whether an act is an exposure or substance administration-deleterious potential, uncertainty of actual transmission, or otherwise. SBADM states that the criterion is the presence of a performer-but there are examples above that call this criterion into question (e.g., the first one, concerning a dosing error).
   */
  Exposure: v3_ActClass_Exposure,
  /**
   * This context represents the part of a patient record conveyed in a single communication. It is drawn from a providing system for the purposes of communication to a requesting process (which might be another repository, a client application or a middleware service such as an electronic guideline engine), and supporting the faithful inclusion of the communicated data in the receiving system.
   *
   *                         An extract may be the entirety of the patient record as held by the sender or it may be a part of that record (e.g. changes since a specified date).
   *
   *                         An extract contains folders or compositions.
   *
   *                         An extract cannot contain another extract.
   */
  Extract: v3_ActClass_Extract,
  /**
   * A contract whose value is measured in monetary terms.
   */
  FinancialContract: v3_ActClass_FinancialContract,
  /**
   * A context representing the high-level organization of an extract e.g. to group parts of the record by episode, care team, clinical specialty, clinical condition, or source application. Internationally, this kind of organizing structure is used variably: in some centers and systems the folder is treated as an informal compartmentalization of the overall health record; in others it might represent a significant legal portion of the EHR relating to the originating enterprise or team.
   *
   *                         A folder contains compositions.
   *
   *                         Folders may be nested within folders.
   */
  Folder: v3_ActClass_Folder,
  /**
   * Description:An observation of genomic phenomena.
   */
  GenomicObservation: v3_ActClass_GenomicObservation,
  /**
   * Definition: An ACT that organizes a set of component acts into a semantic grouping that share a particular context such as timeframe, patient, etc.
   *
   *
   *                            UsageNotes: The focus in a GROUPER act is the grouping of the contained acts.  For example "a request to group" (RQO), "a type of grouping that is allowed to occur" (DEF), etc.
   *
   *                         Unlike WorkingList, which represents a dynamic, shared, continuously updated collection to provide a "view" of a set of objects, GROUPER collections tend to be static and simply indicate a shared set of semantics.  Note that sharing of semantics can be achieved using ACT as well.  However, with GROUPER, the sole semantic is of grouping.
   */
  Grouper: v3_ActClass_Grouper,
  /**
   * A public health case is a Concern about an observation or event that has a specific significance for public health. The creation of a PublicHealthCase initiates the tracking of the object of concern.  The decision to track is related to but somewhat independent of the underlying event or observation.
   *
   *
   *                            UsageNotes: Typically a Public Health Case involves an instance or instances of a reportable infectious disease or other condition. The public health case can include a health-related event concerning a single individual or it may refer to multiple health-related events that are occurrences of the same disease or condition of interest to public health.
   *
   *                         A public health case definition (Act.moodCode = "definition") includes the description of the clinical, laboratory, and epidemiologic indicators associated with a disease or condition of interest to public health. There are case definitions for conditions that are reportable, as well as for those that are not. A public health case definition is a construct used by public health for the purpose of counting cases, and should not be used as clinical indications for treatment. Examples include AIDS, toxic-shock syndrome, and salmonellosis and their associated indicators that are used to define a case.
   */
  PublicHealthCase: v3_ActClass_PublicHealthCase,
  /**
   * An event that occurred outside of the control of one or more of the parties involved.  Includes the concept of an accident.
   */
  Incident: v3_ActClass_Incident,
  /**
   * Sender sends payload to addressee as information.  Addressee does not have responsibilities beyond serving addressee's own interest (i.e., read and memorize if you see fit).  This is equivalent to an FYI on a memo.
   */
  Information: v3_ActClass_Information,
  /**
   * The act  of transmitting information and understanding about a topic to a subject where the participation association must be SBJ.
   *
   *
   *                            Discussion: This act may be used to request that a patient or provider be informed about an Act, or to indicate that a person was informed about a particular act.
   */
  Inform: v3_ActClass_Inform,
  /**
   * Represents concepts related to invoice processing in health care
   */
  InvoiceElement: v3_ActClass_InvoiceElement,
  /**
   * An formalized inquiry into the circumstances surrounding a particular unplanned event or potential event for the purposes of identifying possible causes and contributing factors for the event. This investigation could be conducted at a local institutional level or at the level of a local or national government.
   */
  Investigation: v3_ActClass_Investigation,
  /**
   * Description:A mandate, regulation, obligation, requirement, rule, or expectation unilaterally imposed by a jurisdiction on:
   *
   *
   *
   *                               The activity of another party
   *
   *
   *
   *                               The behavior of another party
   *
   *
   *
   *                               The manner in which an act is executed
   *
   *
   *
   *
   *                            Examples:A jurisdictional mandate regarding the prescribing and dispensing of a particular medication.  A jurisdictional privacy or security regulation dictating the manner in which personal health information is disclosed.  A jurisdictional requirement that certain services or health conditions are reported to a monitoring program, e.g., immunizations, methadone treatment, or cancer registries.
   */
  JurisdictionalPolicy: v3_ActClass_JurisdictionalPolicy,
  LINKHTML: v3_ActClass_LINKHTML,
  /**
   * Working list collects a dynamic list of individual instances of Act via ActRelationship which reflects the need of an individual worker, team of workers, or an organization to manage lists of acts for many different clinical and administrative reasons. Examples of working lists include problem lists, goal lists, allergy lists, and to-do lists.
   */
  WorkingList: v3_ActClass_WorkingList,
  /**
   * Description:The position of a gene (or other significant sequence) on the genome.
   */
  Locus: v3_ActClass_Locus,
  LOCALATTR: v3_ActClass_LOCALATTR,
  LOCALMRKP: v3_ActClass_LOCALMRKP,
  /**
   * An officially or unofficially instituted program to track acts of a particular type or categorization.
   */
  MonitoringProgram: v3_ActClass_MonitoringProgram,
  /**
   * Description:An act that is intended to result in new information about a subject. The main difference between Observations and other Acts is that Observations have a value attribute. The code attribute of Observation and the value attribute of Observation must be considered in combination to determine the semantics of the observation.
   *
   *
   *                            Discussion:
   *
   *
   *                         Structurally, many observations are name-value-pairs, where the Observation.code (inherited from Act) is the name and the Observation.value is the value of the property. Such a construct is also known as a  variable (a named feature that can assume a value) hence, the Observation class is always used to hold generic name-value-pairs or variables, even though the variable valuation may not be the result of an elaborate observation method. It may be a simple answer to a question or it may be an assertion or setting of a parameter.
   *
   *                         As with all Act statements, Observation statements describe what was done, and in the case of Observations, this includes a description of what was actually observed (results or answers); and those results or answers are part of the observation and not split off into other objects.
   *
   *                         The method of action is asserted by the Observation classCode or its subclasses at the least granular level, by the Observation.code attribute value at the medium level of granularity, and by the attribute value of observation.methodCode when a finer level of granularity is required. The method in whole or in part may also appear in the attribute value of Observation.value when using coded data types to express the value of the attribute. Relevant aspects of methodology may also be restated in value when the results themselves imply or state a methodology.
   *
   *                         An observation may consist of component observations each having their own Observation.code and Observation.value. In this case, the composite observation may not have an Observation.value for itself. For instance, a white blood cell count consists of the sub-observations for the counts of the various granulocytes, lymphocytes and other normal or abnormal blood cells (e.g., blasts). The overall white blood cell count Observation itself may therefore not have a value by itself (even though it could have one, e.g., the sum total of white blood cells). Thus, as long as an Act is essentially an Act of recognizing and noting information about a subject, it is an Observation, regardless of whether it has a simple value by itself or whether it has sub-observations.
   *
   *                         Even though observations are professional acts (see Act) and as such are intentional actions, this does not require that every possible outcome of an observation be pondered in advance of it being actually made. For instance, differential white blood cell counts (WBC) rarely show blasts, but if they do, this is part of the WBC observation even though blasts might not be predefined in the structure of a normal WBC.
   *
   *                         Clinical documents commonly have Subjective and Objective findings, both of which are kinds of Observations. In addition, clinical documents commonly contain Assessments, which are also kinds of Observations. Thus, the establishment of a diagnosis is an Observation.
   *
   *
   *                            Examples:
   *
   *
   *
   *
   *                               Recording the results of a Family History Assessment
   *
   *
   *
   *                               Laboratory test and associated result
   *
   *
   *
   *                               Physical exam test and associated result
   *
   *
   *
   *                               Device temperature
   *
   *
   *
   *                               Soil lead level
   */
  Observation: v3_ActClass_Observation,
  /**
   * Container for Observation Sequences (Observations whose values are contained in LIST<>'s) having values correlated with each other.  Each contained Observation Sequence LIST<> must be the same length.  Values in the LIST<>'s are correlated based on index.  E.g. the values in position 2 in all the LIST<>'s are correlated.  This is analogous to a table where each column is an Observation Sequence with a LIST<> of values, and each row in the table is a correlation between the columns.  For example, a 12-lead ECG would contain 13 sequences: one sequence for time, and a sequence for each of the 12 leads.
   */
  CorrelatedObservationSequences: v3_ActClass_CorrelatedObservationSequences,
  /**
   * Container for Correlated Observation Sequences sharing a common frame of reference.  All Observations of the same cd must be comparable and relative to the common frame of reference.  For example, a 3-channel ECG device records a 12-lead ECG in 4 steps (3 leads at a time).  Each of the separate 3-channel recordings would be in their own "OBSCOR".  And, all 4 OBSCOR would be contained in one OBSSER because all the times are relative to the same origin (beginning of the recording) and all the ECG signals were from a fixed set of electrodes.
   */
  ObservationSeries: v3_ActClass_ObservationSeries,
  Ordered: v3_ActClass_Ordered,
  /**
   * Organizer of entries. Navigational. No semantic content. Knowledge of the section code is not required to interpret contained observations. Represents a heading in a heading structure, or "organizer tree".
   *
   *                         The record entries relating to a single clinical session are usually grouped under headings that represent phases of the encounter, or assist with layout and navigation. Clinical headings usually reflect the clinical workflow during a care session, and might also reflect the main author's reasoning processes. Much research has demonstrated that headings are used differently by different professional groups and specialties, and that headings are not used consistently enough to support safe automatic processing of the E H R.
   */
  Organizer: v3_ActClass_Organizer,
  /**
   * Description:A mandate, obligation, requirement, rule, or expectation unilaterally imposed by an organization on:
   *
   *
   *
   *                               The activity of another party
   *
   *
   *
   *                               The behavior of another party
   *
   *
   *
   *                               The manner in which an act is executed
   *
   *
   *
   *
   *                            Examples:A clinical or research protocols imposed by a payer, a malpractice insurer, or an institution to which a provider must adhere.  A mandate imposed by a denominational institution for a provider to provide or withhold certain information from the patient about treatment options.
   */
  OrganizationalPolicy: v3_ActClass_OrganizationalPolicy,
  /**
   * An Outbreak is a concern resulting from a series of public health cases.
   *
   *
   *                            UsageNotes: The date on which an outbreak starts is the earliest date of onset among the cases assigned to the outbreak and its ending date is the last date of onset among the cases assigned to the outbreak. The effectiveTime attribute is used to convey the relevant dates for the case. An outbreak definition (Act.moodCode = "definition" includes the criteria for the number, types and occurrence pattern of cases necessary to declare an outbreak and to judge the severity of an outbreak.
   */
  Outbreak: v3_ActClass_Outbreak,
  /**
   * An Act that of taking on whole or partial responsibility for, or attention to, safety and well-being of a subject of care.
   *
   *
   *                            Discussion: A care provision event may exist without any other care actions taking place. For example, when a patient is assigned to the care of a particular health professional.
   *
   *                         In request (RQO) mood care provision communicates a referral, which is a request:
   *
   *
   *
   *                               from one party (linked as a participant of type author (AUT)),
   *
   *
   *
   *                               to another party (linked as a participant of type performer (PRF),
   *
   *
   *
   *                               to take responsibility for a scope specified by the code attribute,
   *
   *
   *
   *                               for an entity (linked as a participant of type subject (SBJ)).
   *
   *
   *
   *                         The scope of the care for which responsibility is taken is identified by code attribute.
   *
   *                         In event (EVN) mood care provision indicates the effective time interval of a specified scope of responsibility by a performer (PRF) or set of performers (PRF) for a subject (SBJ).
   *
   *
   *                            Examples:
   *
   *
   *
   *
   *                               Referral from GP to a specialist.
   *
   *
   *
   *                               Assignment of a patient or group of patients to the case list of a health professional.
   *
   *
   *
   *                               Assignment of inpatients to the care of particular nurses for a working shift.
   */
  CareProvision: v3_ActClass_CareProvision,
  /**
   * Description:A genomic phenomenon that is expressed externally in the organism.
   */
  Phenotype: v3_ActClass_Phenotype,
  /**
   * Description:A polypeptide resulting from the translation of a gene.
   */
  Polypeptide: v3_ActClass_Polypeptide,
  /**
   * Description:A mandate, regulation, obligation, requirement, rule, or expectation unilaterally imposed by one party on:
   *
   *
   *
   *                               The activity of another party
   *
   *
   *
   *                               The behavior of another party
   *
   *
   *
   *                               The manner in which an act is executed
   */
  Policy: v3_ActClass_Policy,
  /**
   * An observation denoting the physical location of a person or thing based on a reference coordinate system.
   */
  Position: v3_ActClass_Position,
  /**
   * Description:An observation representing the degree to which the assignment of the spatial coordinates, based on a matching algorithm by a geocoding engine against a reference spatial database, matches true or accepted values.
   */
  PositionAccuracy: v3_ActClass_PositionAccuracy,
  /**
   * Description:An observation representing one of a set of numerical values used to determine the position of a place.  The name of the coordinate value is determined by the reference coordinate system.
   */
  PositionCoordinate: v3_ActClass_PositionCoordinate,
  /**
   * An Act whose immediate and primary outcome (post-condition) is the alteration of the physical condition of the subject.
   *
   *
   *                            Examples: : Procedures may involve the disruption of some body surface (e.g. an incision in a surgical procedure), but they also include conservative procedures such as reduction of a luxated join, chiropractic treatment, massage, balneotherapy, acupuncture, shiatsu, etc. Outside of clinical medicine, procedures may be such things as alteration of environments (e.g. straightening rivers, draining swamps, building dams) or the repair or change of machinery etc.
   */
  Procedure: v3_ActClass_Procedure,
  REFR: v3_ActClass_REFR,
  /**
   * Represents the act of maintaining information about the registration of its associated registered subject. The subject can be either an Act or a Role, and includes subjects such as lab exam definitions, drug protocol definitions, prescriptions, persons, patients, practitioners, and equipment.
   *
   *                         The registration may have a unique identifier - separate from the unique identification of the subject - as well as a core set of related participations and act relationships that characterize the registration event and aid in the disposition of the subject information by a receiving system.
   */
  Registration: v3_ActClass_Registration,
  /**
   * The act of examining and evaluating the subject, usually another act. For example, "This prescription needs to be reviewed in 2 months."
   */
  Review: v3_ActClass_Review,
  /**
   * A Region of Interest (ROI) specified for a multidimensional observation, such as an Observation Series (OBSSER). The ROI is specified using a set of observation criteria, each delineating the boundary of the region in one of the dimensions in the multidimensional observation. The relationship between a ROI and its referenced Act is specified through an ActRelationship of type subject (SUBJ), which must always be present. Each of the boundary criteria observations is connected with the ROI using ActRelationships of type "has component" (COMP). In each boundary criterion, the Act.code names the dimension and the Observation.value specifies the range of values inside the region. Typically the bounded dimension is continuous, and so the Observation.value will be an interval (IVL) data type. The Observation.value need not be specified if the respective dimension is only named but not constrained. For example, an ROI for the QT interval of a certain beat in ECG Lead II would contain 2 boundary criteria, one naming the interval in time (constrained), and the other naming the interval in ECG Lead II (only named, but not constrained).
   */
  BoundedROI: v3_ActClass_BoundedROI,
  /**
   * A Region of Interest (ROI) specified for an image using an overlay shape. Typically used to make reference to specific regions in images, e.g., to specify the location of a radiologic finding in an image or to specify the site of a physical finding by "circling" a region in a schematic picture of a human body. The units of the coordinate values are in pixels.  The origin is in the upper left hand corner, with positive X values going to the right and positive Y values going down. The relationship between a ROI and its referenced Act is specified through an ActRelationship of type "subject" (SUBJ), which must always be present.
   */
  OverlayROI: v3_ActClass_OverlayROI,
  /**
   * The act of introducing or otherwise applying a substance to the subject.
   *
   *
   *                            Discussion: The effect of the substance is typically established on a biochemical basis, however, that is not a requirement. For example, radiotherapy can largely be described in the same way, especially if it is a systemic therapy such as radio-iodine.  This class also includes the application of chemical treatments to an area.
   *
   *
   *                            Examples: Chemotherapy protocol; Drug prescription; Vaccination record
   */
  SubstanceAdministration: v3_ActClass_SubstanceAdministration,
  /**
   * Description: The act of removing a substance from the subject.
   */
  SubstanceExtraction: v3_ActClass_SubstanceExtraction,
  /**
   * Description:An ethical or clinical obligation, requirement, rule, or expectation imposed or strongly encouraged by organizations that oversee particular clinical domains or provider certification which define the boundaries within which a provider may practice and which may have legal basis or ramifications on:
   *
   *
   *
   *                               The activity of another party
   *
   *
   *
   *                               The behavior of another party
   *
   *
   *
   *                               The manner in which an act is executed
   *
   *
   *
   *
   *                            Examples:An ethical obligation for a provider to fully inform a patient about all treatment options.  An ethical obligation for a provider not to disclose personal health information that meets certain criteria, e.g., where disclosure might result in harm to the patient or another person.  The set of health care services which a provider is credentialed or privileged to provide.
   */
  ScopeOfPracticePolicy: v3_ActClass_ScopeOfPracticePolicy,
  /**
   * Description:A sequence of biomolecule like the DNA, RNA, protein and the like.
   */
  BioSequence: v3_ActClass_BioSequence,
  /**
   * Description:A variation in a sequence as described by BioSequence.
   */
  BioSequenceVariation: v3_ActClass_BioSequenceVariation,
  /**
   * An observation on a specimen in a laboratory environment that may affect processing, analysis or result interpretation
   */
  SpecimenObservation: v3_ActClass_SpecimenObservation,
  /**
   * A procedure or treatment performed on a specimen to prepare it for analysis
   */
  SpecimenTreatment: v3_ActClass_SpecimenTreatment,
  /**
   * A procedure for obtaining a specimen from a source entity.
   */
  SpecimenCollection: v3_ActClass_SpecimenCollection,
  /**
   * Supply orders and deliveries are simple Acts that focus on the delivered product. The product is associated with the Supply Act via Participation.typeCode="product". With general Supply Acts, the precise identification of the Material (manufacturer, serial numbers, etc.) is important.  Most of the detailed information about the Supply should be represented using the Material class.  If delivery needs to be scheduled, tracked, and billed separately, one can associate a Transportation Act with the Supply Act.  Pharmacy dispense services are represented as Supply Acts, associated with a SubstanceAdministration  Act. The SubstanceAdministration class represents the administration of medication, while dispensing is supply.
   */
  Supply: v3_ActClass_Supply,
  /**
   * Description: Sender transmits a status change pertaining to the focal act of the payload. This status of the focal act is the final state of the state transition. This can be either a request or an event, according to the mood of the control act.
   */
  StateTransitionControl: v3_ActClass_StateTransitionControl,
  /**
   * Description:A requirement, rule, or expectation typically documented as guidelines, protocols, or formularies imposed or strongly encouraged by an organization that oversees or has authority over the practices within a domain, and which may have legal basis or ramifications on:
   *
   *
   *
   *                               The activity of another party
   *
   *
   *
   *                               The behavior of another party
   *
   *
   *
   *                               The manner in which an act is executed
   *
   *
   *
   *
   *                            Examples:A payer may require a prescribing provider to adhere to formulary guidelines.  An institution may adopt clinical guidelines and protocols and implement these within its electronic health record and decision support systems.
   */
  StandardOfPracticePolicy: v3_ActClass_StandardOfPracticePolicy,
  /**
   * The act of putting something away for safe keeping. The "something" may be physical object such as a specimen, or information, such as observations regarding a specimen.
   */
  Storage: v3_ActClass_Storage,
  /**
   * Definition: Indicates that the subject Act has undergone or should undergo substitution of a type indicated by Act.code.
   *
   *                         Rationale: Used to specify "allowed" substitution when creating orders, "actual" susbstitution when sending events, as well as the reason for the substitution and who was responsible for it.
   */
  Substitution: v3_ActClass_Substitution,
  TBLCOL: v3_ActClass_TBLCOL,
  TBLCOLGP: v3_ActClass_TBLCOLGP,
  TBLDATA: v3_ActClass_TBLDATA,
  TBLHDR: v3_ActClass_TBLHDR,
  TBLROW: v3_ActClass_TBLROW,
  Tbody: v3_ActClass_Tbody,
  /**
   * Description:
   *
   *
   *                         A transmission exposure act describes the proximity (time and location) over which the participating source entity was capable of transmitting a physical (including energy), chemical or biological substance agent to another entity.  The transmission exposure act is used in conjunction with acquisition exposure acts as part of an analysis technique for contact tracing.  Although an exposure can be decomposed into transmission and acquisition exposures, there is no requirement that all exposures be treated in this fashion.
   *
   *
   *                            Constraints:  The Transmission Exposure inherits the participation constraints that apply to Exposure with the following exception.  The EXPTRGT (exposure target) participation must never be associated with the Transmission Exposure either directly or via context conduction.
   */
  TransmissionExposure: v3_ActClass_TransmissionExposure,
  Tfoot: v3_ActClass_Tfoot,
  Thead: v3_ActClass_Thead,
  /**
   * A group of entries within a composition that are related to a common clinical theme - such as a specific disorder or problem, prevention, screening and provision of contraceptive services.
   *
   *                         A topic may contain categories and entries.
   */
  Topic: v3_ActClass_Topic,
  /**
   * Definition: The act of transferring information without the intent of imparting understanding about a topic to the subject that is the recipient or holder of the transferred information where the participation association must be RCV or HLD.
   */
  Transfer: v3_ActClass_Transfer,
  /**
   * Transportation is the moving of a payload (people or material) from a location of origin to a destination location.  Thus, any transport service has the three target instances of type payload, origin, and destination, besides the targets that are generally used for any service (i.e., performer, device, etc.)
   */
  Transportation: v3_ActClass_Transportation,
  Unordered: v3_ActClass_Unordered,
  /**
   * An act which describes the process whereby a 'verifying party' validates either the existence of the Role attested to by some Credential or the actual Vetting act and its details.
   */
  Verification: v3_ActClass_Verification,
  /**
   * A sub-class of Act representing any transaction between two accounts whose value is measured in monetary terms.
   *
   *                         In the "intent" mood, communicates a request for a transaction to be initiated, or communicates a transfer of value between two accounts.
   *
   *                         In the "event" mood, communicates the posting of a transaction to an account.
   */
  FinancialTransaction: v3_ActClass_FinancialTransaction,
};
/**
 *  A clinical document is a documentation of clinical observations and services, with the following characteristics: (1) Persistence - A clinical document continues to exist in an unaltered state, for a time period defined by local and regulatory requirements; (2) Stewardship - A clinical document is maintained by a person or organization entrusted with its care; (3) Potential for authentication - A clinical document is an assemblage of information that is intended to be legally authenticated; (4) Wholeness - Authentication of a clinical document applies to the whole and does not apply to portions of the document without the full context of the document; (5) Human readability - A clinical document is human readable."
 */
export const V3ActClassClinicalDocument = {
  /**
   * A clinical document that conforms to Level One of the HL7 Clinical Document Architecture (CDA)
   */
  CDALevelOneClinicalDocument: v3_ActClass_CDALevelOneClinicalDocument,
  /**
   * A clinical document is a documentation of clinical observations and services, with the following characteristics:
   *
   *
   *
   *                               Persistence - A clinical document continues to exist in an unaltered state, for a time period defined by local and regulatory requirements;
   *
   *
   *
   *                               Stewardship - A clinical document is maintained by a person or organization entrusted with its care;
   *
   *
   *
   *                               Potential for authentication - A clinical document is an assemblage of information that is intended to be legally authenticated;
   *
   *
   *
   *                               Wholeness - Authentication of a clinical document applies to the whole and does not apply to portions of the document without the full context of the document;
   *
   *
   *
   *                               Human readability - A clinical document is human readable.
   */
  ClinicalDocument: v3_ActClass_ClinicalDocument,
};
/**
 *  Specialization of Act to add the characteristics unique to document management services.
 */
export const V3ActClassDocument = {
  /**
   * A clinical document that conforms to Level One of the HL7 Clinical Document Architecture (CDA)
   */
  CDALevelOneClinicalDocument: v3_ActClass_CDALevelOneClinicalDocument,
  /**
   * The notion of a document comes particularly from the paper world, where it corresponds to the contents recorded on discrete pieces of paper. In the electronic world, a document is a kind of composition that bears resemblance to their paper world counter-parts. Documents typically are meant to be human-readable.
   *
   *                         HL7's notion of document differs from that described in the W3C XML Recommendation, in which a document refers specifically to the contents that fall between the root element's start-tag and end-tag. Not all XML documents are HL7 documents.
   */
  Document: v3_ActClass_Document,
  /**
   * A clinical document is a documentation of clinical observations and services, with the following characteristics:
   *
   *
   *
   *                               Persistence - A clinical document continues to exist in an unaltered state, for a time period defined by local and regulatory requirements;
   *
   *
   *
   *                               Stewardship - A clinical document is maintained by a person or organization entrusted with its care;
   *
   *
   *
   *                               Potential for authentication - A clinical document is an assemblage of information that is intended to be legally authenticated;
   *
   *
   *
   *                               Wholeness - Authentication of a clinical document applies to the whole and does not apply to portions of the document without the full context of the document;
   *
   *
   *
   *                               Human readability - A clinical document is human readable.
   */
  ClinicalDocument: v3_ActClass_ClinicalDocument,
};
/**
 *  An formalized inquiry into the circumstances surrounding a particular unplanned event or potential event for the purposes of identifying possible causes and contributing factors for the event. This investigation could be conducted at a local institutional level or at the level of a local or national government.
 */
export const V3ActClassInvestigation = {
  /**
   * An formalized inquiry into the circumstances surrounding a particular unplanned event or potential event for the purposes of identifying possible causes and contributing factors for the event. This investigation could be conducted at a local institutional level or at the level of a local or national government.
   */
  Investigation: v3_ActClass_Investigation,
};
/**
 *   Description:
 * An act that is intended to result in new information about a subject. The main difference between Observations and other Acts is that Observations have a value attribute. Thecode
 * attribute of Observation and thevalue
 * attribute of Observation must be considered in combination to determine the semantics of the observation.  Discussion:
 *  Structurally, many observations are name-value-pairs, where the Observation.code (inherited from Act) is the name and the Observation.value is the value of the property. Such a construct is also known as a  variable (a named feature that can assume a value) hence, the Observation class is always used to hold generic name-value-pairs or variables, even though the variable valuation may not be the result of an elaborate observation method. It may be a simple answer to a question or it may be an assertion or setting of a parameter. As with all Act statements, Observation statements describe what was done, and in the case of Observations, this includes a description of what was actually observed (results or answers); and those results or answers are part of the observation and not split off into other objects. The method of action is asserted by the Observation classCode or its subclasses at the least granular level, by the Observation.code attribute value at the medium level of granularity, and by the attribute value of observation.methodCode when a finer level of granularity is required. The method in whole or in part may also appear in the attribute value of Observation.value when using coded data types to express the value of the attribute. Relevant aspects of methodology may also be restated in value when the results themselves imply or state a methodology. An observation may consist of component observations each having their own Observation.code and Observation.value. In this case, the composite observation may not have an Observation.value for itself. For instance, a white blood cell count consists of the sub-observations for the counts of the various granulocytes, lymphocytes and other normal or abnormal blood cells (e.g., blasts). The overall white blood cell count Observation itself may therefore not have a value by itself (even though it could have one, e.g., the sum total of white blood cells). Thus, as long as an Act is essentially an Act of recognizing and noting information about a subject, it is an Observation, regardless of whether it has a simple value by itself or whether it has sub-observations. Even though observations are professional acts (see Act) and as such are intentional actions, this does not require that every possible outcome of an observation be pondered in advance of it being actually made. For instance, differential white blood cell counts (WBC) rarely show blasts, but if they do, this is part of the WBC observation even though blasts might not be predefined in the structure of a normal WBC. Clinical documents commonly have Subjective and Objective findings, both of which are kinds of Observations. In addition, clinical documents commonly contain Assessments, which are also kinds of Observations. Thus, the establishment of a diagnosis is an Observation.  Examples:
 *    Recording the results of a Family History Assessment  Laboratory test and associated result  Physical exam test and associated result  Device temperature  Soil lead level
 */
export const V3ActClassObservation = {
  /**
   * Regions of Interest (ROI) within a subject Act. Primarily used for making secondary observations on a subset of a subject observation. The relationship between a ROI and its referenced Act is specified through an ActRelationship of type "subject" (SUBJ), which must always be present.
   */
  ActClassROI: v3_ActClass_ActClassROI,
  /**
   * The spatial relationship of a subject whether human, other animal, or plant, to a frame of reference such as gravity or a collection device.
   */
  SubjectPhysicalPosition: v3_ActClass_SubjectPhysicalPosition,
  /**
   * An observation identifying a potential adverse outcome as a result of an Act or combination of Acts.
   *
   *
   *                            Examples: Detection of a drug-drug interaction; Identification of a late-submission for an invoice; Requesting discharge for a patient who does not meet hospital-defined discharge criteria.
   *
   *
   *                            Discussion: This class is commonly used for identifying 'business rule' or 'process' problems that may result in a refusal to carry out a particular request. In some circumstances it may be possible to 'bypass' a problem by modifying the request to acknowledge the issue and/or by providing some form of mitigation.
   *
   *
   *                            Constraints: the Act or Acts that may cause the the adverse outcome are the target of a subject ActRelationship. The subbtypes of this concept indicate the type of problem being detected (e.g. drug-drug interaction) while the Observation.value is used to repesent a specific problem code (e.g. specific drug-drug interaction id).
   */
  DetectedIssue: v3_ActClass_DetectedIssue,
  /**
   * Definition: An observation that is composed of a set of observations. These observations typically have a logical or practical grouping for generally accepted clinical or functional purposes, such as observations that are run together because of automation. A battery can define required and optional component observations and, in some cases, will define complex rules that determine whether or not a particular observation is made. BATTERY is a constraint on the Observation class in that it is understood to always be composed of component observations.
   *
   *
   *                            UsageNotes: The focus in a BATTERY is that it is composed of individual observations. In request (RQO) mood, a battery is a request to perform the component observations. In event (EVN) mood a battery is a reporting of associated set of observation events. In definition mood a battery is the definition of the associated set of observations.
   *
   *
   *                            Examples: Vital signs, Full blood count, Chemistry panel.
   */
  Battery: v3_ActClass_Battery,
  /**
   * The set of actions that define an experiment to assess the effectiveness and/or safety of a biopharmaceutical product (food, drug, device, etc.).  In definition mood, this set of actions is often embodied in a clinical trial protocol; in event mood, this designates the aggregate act of applying the actions to one or more subjects.
   */
  ClinicalTrial: v3_ActClass_ClinicalTrial,
  /**
   * Description:A determinant peptide in a polypeptide as described by polypeptide.
   */
  DeterminantPeptide: v3_ActClass_DeterminantPeptide,
  /**
   * Class for holding attributes unique to diagnostic images.
   */
  DiagnosticImage: v3_ActClass_DiagnosticImage,
  /**
   * Description:An expression level of genes/proteins or other expressed genomic entities.
   */
  ExpressionLevel: v3_ActClass_ExpressionLevel,
  /**
   * Description:An observation of genomic phenomena.
   */
  GenomicObservation: v3_ActClass_GenomicObservation,
  /**
   * An formalized inquiry into the circumstances surrounding a particular unplanned event or potential event for the purposes of identifying possible causes and contributing factors for the event. This investigation could be conducted at a local institutional level or at the level of a local or national government.
   */
  Investigation: v3_ActClass_Investigation,
  /**
   * Description:The position of a gene (or other significant sequence) on the genome.
   */
  Locus: v3_ActClass_Locus,
  /**
   * Description:An act that is intended to result in new information about a subject. The main difference between Observations and other Acts is that Observations have a value attribute. The code attribute of Observation and the value attribute of Observation must be considered in combination to determine the semantics of the observation.
   *
   *
   *                            Discussion:
   *
   *
   *                         Structurally, many observations are name-value-pairs, where the Observation.code (inherited from Act) is the name and the Observation.value is the value of the property. Such a construct is also known as a  variable (a named feature that can assume a value) hence, the Observation class is always used to hold generic name-value-pairs or variables, even though the variable valuation may not be the result of an elaborate observation method. It may be a simple answer to a question or it may be an assertion or setting of a parameter.
   *
   *                         As with all Act statements, Observation statements describe what was done, and in the case of Observations, this includes a description of what was actually observed (results or answers); and those results or answers are part of the observation and not split off into other objects.
   *
   *                         The method of action is asserted by the Observation classCode or its subclasses at the least granular level, by the Observation.code attribute value at the medium level of granularity, and by the attribute value of observation.methodCode when a finer level of granularity is required. The method in whole or in part may also appear in the attribute value of Observation.value when using coded data types to express the value of the attribute. Relevant aspects of methodology may also be restated in value when the results themselves imply or state a methodology.
   *
   *                         An observation may consist of component observations each having their own Observation.code and Observation.value. In this case, the composite observation may not have an Observation.value for itself. For instance, a white blood cell count consists of the sub-observations for the counts of the various granulocytes, lymphocytes and other normal or abnormal blood cells (e.g., blasts). The overall white blood cell count Observation itself may therefore not have a value by itself (even though it could have one, e.g., the sum total of white blood cells). Thus, as long as an Act is essentially an Act of recognizing and noting information about a subject, it is an Observation, regardless of whether it has a simple value by itself or whether it has sub-observations.
   *
   *                         Even though observations are professional acts (see Act) and as such are intentional actions, this does not require that every possible outcome of an observation be pondered in advance of it being actually made. For instance, differential white blood cell counts (WBC) rarely show blasts, but if they do, this is part of the WBC observation even though blasts might not be predefined in the structure of a normal WBC.
   *
   *                         Clinical documents commonly have Subjective and Objective findings, both of which are kinds of Observations. In addition, clinical documents commonly contain Assessments, which are also kinds of Observations. Thus, the establishment of a diagnosis is an Observation.
   *
   *
   *                            Examples:
   *
   *
   *
   *
   *                               Recording the results of a Family History Assessment
   *
   *
   *
   *                               Laboratory test and associated result
   *
   *
   *
   *                               Physical exam test and associated result
   *
   *
   *
   *                               Device temperature
   *
   *
   *
   *                               Soil lead level
   */
  Observation: v3_ActClass_Observation,
  /**
   * Container for Observation Sequences (Observations whose values are contained in LIST<>'s) having values correlated with each other.  Each contained Observation Sequence LIST<> must be the same length.  Values in the LIST<>'s are correlated based on index.  E.g. the values in position 2 in all the LIST<>'s are correlated.  This is analogous to a table where each column is an Observation Sequence with a LIST<> of values, and each row in the table is a correlation between the columns.  For example, a 12-lead ECG would contain 13 sequences: one sequence for time, and a sequence for each of the 12 leads.
   */
  CorrelatedObservationSequences: v3_ActClass_CorrelatedObservationSequences,
  /**
   * Container for Correlated Observation Sequences sharing a common frame of reference.  All Observations of the same cd must be comparable and relative to the common frame of reference.  For example, a 3-channel ECG device records a 12-lead ECG in 4 steps (3 leads at a time).  Each of the separate 3-channel recordings would be in their own "OBSCOR".  And, all 4 OBSCOR would be contained in one OBSSER because all the times are relative to the same origin (beginning of the recording) and all the ECG signals were from a fixed set of electrodes.
   */
  ObservationSeries: v3_ActClass_ObservationSeries,
  /**
   * Description:A genomic phenomenon that is expressed externally in the organism.
   */
  Phenotype: v3_ActClass_Phenotype,
  /**
   * Description:A polypeptide resulting from the translation of a gene.
   */
  Polypeptide: v3_ActClass_Polypeptide,
  /**
   * An observation denoting the physical location of a person or thing based on a reference coordinate system.
   */
  Position: v3_ActClass_Position,
  /**
   * Description:An observation representing the degree to which the assignment of the spatial coordinates, based on a matching algorithm by a geocoding engine against a reference spatial database, matches true or accepted values.
   */
  PositionAccuracy: v3_ActClass_PositionAccuracy,
  /**
   * Description:An observation representing one of a set of numerical values used to determine the position of a place.  The name of the coordinate value is determined by the reference coordinate system.
   */
  PositionCoordinate: v3_ActClass_PositionCoordinate,
  /**
   * A Region of Interest (ROI) specified for a multidimensional observation, such as an Observation Series (OBSSER). The ROI is specified using a set of observation criteria, each delineating the boundary of the region in one of the dimensions in the multidimensional observation. The relationship between a ROI and its referenced Act is specified through an ActRelationship of type subject (SUBJ), which must always be present. Each of the boundary criteria observations is connected with the ROI using ActRelationships of type "has component" (COMP). In each boundary criterion, the Act.code names the dimension and the Observation.value specifies the range of values inside the region. Typically the bounded dimension is continuous, and so the Observation.value will be an interval (IVL) data type. The Observation.value need not be specified if the respective dimension is only named but not constrained. For example, an ROI for the QT interval of a certain beat in ECG Lead II would contain 2 boundary criteria, one naming the interval in time (constrained), and the other naming the interval in ECG Lead II (only named, but not constrained).
   */
  BoundedROI: v3_ActClass_BoundedROI,
  /**
   * A Region of Interest (ROI) specified for an image using an overlay shape. Typically used to make reference to specific regions in images, e.g., to specify the location of a radiologic finding in an image or to specify the site of a physical finding by "circling" a region in a schematic picture of a human body. The units of the coordinate values are in pixels.  The origin is in the upper left hand corner, with positive X values going to the right and positive Y values going down. The relationship between a ROI and its referenced Act is specified through an ActRelationship of type "subject" (SUBJ), which must always be present.
   */
  OverlayROI: v3_ActClass_OverlayROI,
  /**
   * Description:A sequence of biomolecule like the DNA, RNA, protein and the like.
   */
  BioSequence: v3_ActClass_BioSequence,
  /**
   * Description:A variation in a sequence as described by BioSequence.
   */
  BioSequenceVariation: v3_ActClass_BioSequenceVariation,
  /**
   * An observation on a specimen in a laboratory environment that may affect processing, analysis or result interpretation
   */
  SpecimenObservation: v3_ActClass_SpecimenObservation,
  /**
   * An act which describes the process whereby a 'verifying party' validates either the existence of the Role attested to by some Credential or the actual Vetting act and its details.
   */
  Verification: v3_ActClass_Verification,
};
/**
 *  An Act whose immediate and primary outcome (post-condition) is the alteration of the physical condition of the subject.  Examples:
 * : Procedures may involve the disruption of some body surface (e.g. an incision in a surgical procedure), but they also include conservative procedures such as reduction of a luxated join, chiropractic treatment, massage, balneotherapy, acupuncture, shiatsu, etc. Outside of clinical medicine, procedures may be such things as alteration of environments (e.g. straightening rivers, draining swamps, building dams) or the repair or change of machinery etc.
 */
export const V3ActClassProcedure = {
  /**
   * An Act whose immediate and primary outcome (post-condition) is the alteration of the physical condition of the subject.
   *
   *
   *                            Examples: : Procedures may involve the disruption of some body surface (e.g. an incision in a surgical procedure), but they also include conservative procedures such as reduction of a luxated join, chiropractic treatment, massage, balneotherapy, acupuncture, shiatsu, etc. Outside of clinical medicine, procedures may be such things as alteration of environments (e.g. straightening rivers, draining swamps, building dams) or the repair or change of machinery etc.
   */
  Procedure: v3_ActClass_Procedure,
  /**
   * The act of introducing or otherwise applying a substance to the subject.
   *
   *
   *                            Discussion: The effect of the substance is typically established on a biochemical basis, however, that is not a requirement. For example, radiotherapy can largely be described in the same way, especially if it is a systemic therapy such as radio-iodine.  This class also includes the application of chemical treatments to an area.
   *
   *
   *                            Examples: Chemotherapy protocol; Drug prescription; Vaccination record
   */
  SubstanceAdministration: v3_ActClass_SubstanceAdministration,
  /**
   * Description: The act of removing a substance from the subject.
   */
  SubstanceExtraction: v3_ActClass_SubstanceExtraction,
  /**
   * A procedure for obtaining a specimen from a source entity.
   */
  SpecimenCollection: v3_ActClass_SpecimenCollection,
};
/**
 *  Regions of Interest (ROI) within a subject Act. Primarily used for making secondary observations on a subset of a subject observation. The relationship between a ROI and its referenced Act is specified through an ActRelationship of type "subject" (SUBJ), which must always be present.
 */
export const V3ActClassROI = {
  /**
   * A Region of Interest (ROI) specified for a multidimensional observation, such as an Observation Series (OBSSER). The ROI is specified using a set of observation criteria, each delineating the boundary of the region in one of the dimensions in the multidimensional observation. The relationship between a ROI and its referenced Act is specified through an ActRelationship of type subject (SUBJ), which must always be present. Each of the boundary criteria observations is connected with the ROI using ActRelationships of type "has component" (COMP). In each boundary criterion, the Act.code names the dimension and the Observation.value specifies the range of values inside the region. Typically the bounded dimension is continuous, and so the Observation.value will be an interval (IVL) data type. The Observation.value need not be specified if the respective dimension is only named but not constrained. For example, an ROI for the QT interval of a certain beat in ECG Lead II would contain 2 boundary criteria, one naming the interval in time (constrained), and the other naming the interval in ECG Lead II (only named, but not constrained).
   */
  BoundedROI: v3_ActClass_BoundedROI,
  /**
   * A Region of Interest (ROI) specified for an image using an overlay shape. Typically used to make reference to specific regions in images, e.g., to specify the location of a radiologic finding in an image or to specify the site of a physical finding by "circling" a region in a schematic picture of a human body. The units of the coordinate values are in pixels.  The origin is in the upper left hand corner, with positive X values going to the right and positive Y values going down. The relationship between a ROI and its referenced Act is specified through an ActRelationship of type "subject" (SUBJ), which must always be present.
   */
  OverlayROI: v3_ActClass_OverlayROI,
};
/**
 *  Supply orders and deliveries are simple Acts that focus on the delivered product. The product is associated with the Supply Act via Participation.typeCode="product". With general Supply Acts, the precise identification of the Material (manufacturer, serial numbers, etc.) is important.  Most of the detailed information about the Supply should be represented using the Material class.  If delivery needs to be scheduled, tracked, and billed separately, one can associate a Transportation Act with the Supply Act.  Pharmacy dispense services are represented as Supply Acts, associated with a SubstanceAdministration  Act. The SubstanceAdministration class represents the administration of medication, while dispensing is supply.
 */
export const V3ActClassSupply = {
  /**
   * Supply orders and deliveries are simple Acts that focus on the delivered product. The product is associated with the Supply Act via Participation.typeCode="product". With general Supply Acts, the precise identification of the Material (manufacturer, serial numbers, etc.) is important.  Most of the detailed information about the Supply should be represented using the Material class.  If delivery needs to be scheduled, tracked, and billed separately, one can associate a Transportation Act with the Supply Act.  Pharmacy dispense services are represented as Supply Acts, associated with a SubstanceAdministration  Act. The SubstanceAdministration class represents the administration of medication, while dispensing is supply.
   */
  Supply: v3_ActClass_Supply,
};
const v3_ActCode_ActAdjudicationCode: Coding = {
  code: '_ActAdjudicationCode',
  display: 'ActAdjudicationCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActAdjudicationGroupCode: Coding = {
  code: '_ActAdjudicationGroupCode',
  display: 'ActAdjudicationGroupCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActAdjudicationResultActionCode: Coding = {
  code: '_ActAdjudicationResultActionCode',
  display: 'ActAdjudicationResultActionCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActAdministrativeAuthorizationDetectedIssueCode: Coding = {
  code: '_ActAdministrativeAuthorizationDetectedIssueCode',
  display: 'ActAdministrativeAuthorizationDetectedIssueCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActAdministrativeDetectedIssueCode: Coding = {
  code: '_ActAdministrativeDetectedIssueCode',
  display: 'ActAdministrativeDetectedIssueCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActAdministrativeRuleDetectedIssueCode: Coding = {
  code: '_ActAdministrativeRuleDetectedIssueCode',
  display: 'ActAdministrativeRuleDetectedIssueCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActBillableModifierCode: Coding = {
  code: '_ActBillableModifierCode',
  display: 'ActBillableModifierCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActBillableServiceCode: Coding = {
  code: '_ActBillableServiceCode',
  display: 'ActBillableServiceCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActBillingArrangementCode: Coding = {
  code: '_ActBillingArrangementCode',
  display: 'ActBillingArrangementCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActBoundedROICode: Coding = {
  code: '_ActBoundedROICode',
  display: 'ActBoundedROICode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActCareProvision: Coding = {
  code: '_ActCareProvisionCode',
  display: 'act care provision',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActClaimAttachmentCategoryCode: Coding = {
  code: '_ActClaimAttachmentCategoryCode',
  display: 'ActClaimAttachmentCategoryCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActConsentDirective: Coding = {
  code: '_ActConsentDirective',
  display: 'ActConsentDirective',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActConsentType: Coding = {
  code: '_ActConsentType',
  display: 'ActConsentType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActContainerRegistrationCode: Coding = {
  code: '_ActContainerRegistrationCode',
  display: 'ActContainerRegistrationCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActControlVariable: Coding = {
  code: '_ActControlVariable',
  display: 'ActControlVariable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActCoverageAuthorizationConfirmationCode: Coding = {
  code: '_ActCoverageAuthorizationConfirmationCode',
  display: 'ActCoverageAuthorizationConfirmationCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActCoverageConfirmationCode: Coding = {
  code: '_ActCoverageConfirmationCode',
  display: 'ActCoverageConfirmationCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActCoverageEligibilityConfirmationCode: Coding = {
  code: '_ActCoverageEligibilityConfirmationCode',
  display: 'ActCoverageEligibilityConfirmationCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActCoverageLimitCode: Coding = {
  code: '_ActCoverageLimitCode',
  display: 'ActCoverageLimitCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActCoverageQuantityLimitCode: Coding = {
  code: '_ActCoverageQuantityLimitCode',
  display: 'ActCoverageQuantityLimitCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActCoverageTypeCode: Coding = {
  code: '_ActCoverageTypeCode',
  display: 'ActCoverageTypeCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActCoveredPartyLimitCode: Coding = {
  code: '_ActCoveredPartyLimitCode',
  display: 'ActCoveredPartyLimitCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActCredentialedCare: Coding = {
  code: '_ActCredentialedCareCode',
  display: 'act credentialed care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActCredentialedCareProvisionPeron: Coding = {
  code: '_ActCredentialedCareProvisionPersonCode',
  display: 'act credentialed care provision peron',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActCredentialedCareProvisionProgram: Coding = {
  code: '_ActCredentialedCareProvisionProgramCode',
  display: 'act credentialed care provision program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActEncounterAccommodationCode: Coding = {
  code: '_ActEncounterAccommodationCode',
  display: 'ActEncounterAccommodationCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActEncounterCode: Coding = {
  code: '_ActEncounterCode',
  display: 'ActEncounterCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActExposureCode: Coding = {
  code: '_ActExposureCode',
  display: 'ActExposureCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActFinancialTransactionCode: Coding = {
  code: '_ActFinancialTransactionCode',
  display: 'ActFinancialTransactionCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActHealthInsuranceTypeCode: Coding = {
  code: '_ActHealthInsuranceTypeCode',
  display: 'ActHealthInsuranceTypeCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActIncidentCode: Coding = {
  code: '_ActIncidentCode',
  display: 'ActIncidentCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInformationAccessCode: Coding = {
  code: '_ActInformationAccessCode',
  display: 'ActInformationAccessCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInformationAccessContextCode: Coding = {
  code: '_ActInformationAccessContextCode',
  display: 'ActInformationAccessContextCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInformationCategoryCode: Coding = {
  code: '_ActInformationCategoryCode',
  display: 'ActInformationCategoryCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInformationSensitivityPolicy: Coding = {
  code: '_ActInformationSensitivityPolicy',
  display: 'ActInformationSensitivityPolicy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInsurancePolicyCode: Coding = {
  code: '_ActInsurancePolicyCode',
  display: 'ActInsurancePolicyCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInsuranceTypeCode: Coding = {
  code: '_ActInsuranceTypeCode',
  display: 'ActInsuranceTypeCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceAdjudicationPaymentCode: Coding = {
  code: '_ActInvoiceAdjudicationPaymentCode',
  display: 'ActInvoiceAdjudicationPaymentCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceAdjudicationPaymentGroupCode: Coding = {
  code: '_ActInvoiceAdjudicationPaymentGroupCode',
  display: 'ActInvoiceAdjudicationPaymentGroupCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceAdjudicationPaymentSummaryCode: Coding = {
  code: '_ActInvoiceAdjudicationPaymentSummaryCode',
  display: 'ActInvoiceAdjudicationPaymentSummaryCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceDetailClinicalProductCode: Coding = {
  code: '_ActInvoiceDetailClinicalProductCode',
  display: 'ActInvoiceDetailClinicalProductCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceDetailClinicalServiceCode: Coding = {
  code: '_ActInvoiceDetailClinicalServiceCode',
  display: 'ActInvoiceDetailClinicalServiceCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceDetailCode: Coding = {
  code: '_ActInvoiceDetailCode',
  display: 'ActInvoiceDetailCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceDetailDrugProductCode: Coding = {
  code: '_ActInvoiceDetailDrugProductCode',
  display: 'ActInvoiceDetailDrugProductCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceDetailGenericAdjudicatorCode: Coding = {
  code: '_ActInvoiceDetailGenericAdjudicatorCode',
  display: 'ActInvoiceDetailGenericAdjudicatorCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceDetailGenericCode: Coding = {
  code: '_ActInvoiceDetailGenericCode',
  display: 'ActInvoiceDetailGenericCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceDetailGenericModifierCode: Coding = {
  code: '_ActInvoiceDetailGenericModifierCode',
  display: 'ActInvoiceDetailGenericModifierCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceDetailGenericProviderCode: Coding = {
  code: '_ActInvoiceDetailGenericProviderCode',
  display: 'ActInvoiceDetailGenericProviderCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceDetailPreferredAccommodationCode: Coding = {
  code: '_ActInvoiceDetailPreferredAccommodationCode',
  display: 'ActInvoiceDetailPreferredAccommodationCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceDetailTaxCode: Coding = {
  code: '_ActInvoiceDetailTaxCode',
  display: 'ActInvoiceDetailTaxCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceElementCode: Coding = {
  code: '_ActInvoiceElementCode',
  display: 'ActInvoiceElementCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceElementSummaryCode: Coding = {
  code: '_ActInvoiceElementSummaryCode',
  display: 'ActInvoiceElementSummaryCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceGroupCode: Coding = {
  code: '_ActInvoiceGroupCode',
  display: 'ActInvoiceGroupCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceInterGroupCode: Coding = {
  code: '_ActInvoiceInterGroupCode',
  display: 'ActInvoiceInterGroupCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceOverrideCode: Coding = {
  code: '_ActInvoiceOverrideCode',
  display: 'ActInvoiceOverrideCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActInvoiceRootGroupCode: Coding = {
  code: '_ActInvoiceRootGroupCode',
  display: 'ActInvoiceRootGroupCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActListCode: Coding = {
  code: '_ActListCode',
  display: 'ActListCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActMedicalServiceCode: Coding = {
  code: '_ActMedicalServiceCode',
  display: 'ActMedicalServiceCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActMedicationTherapyDurationWorkingList: Coding = {
  code: '_ActMedicationTherapyDurationWorkingListCode',
  display: 'act medication therapy duration working list',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActMonitoringProtocolCode: Coding = {
  code: '_ActMonitoringProtocolCode',
  display: 'ActMonitoringProtocolCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActNonObservationIndicationCode: Coding = {
  code: '_ActNonObservationIndicationCode',
  display: 'ActNonObservationIndicationCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActObservationList: Coding = {
  code: '_ActObservationList',
  display: 'ActObservationList',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActObservationVerification: Coding = {
  code: '_ActObservationVerificationType',
  display: 'act observation verification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActPatientAnnotationType: Coding = {
  code: '_ActPatientAnnotationType',
  display: 'ActPatientAnnotationType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActPatientTransportationModeCode: Coding = {
  code: '_ActPatientTransportationModeCode',
  display: 'ActPatientTransportationModeCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActPaymentCode: Coding = {
  code: '_ActPaymentCode',
  display: 'ActPaymentCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActPharmacySupplyType: Coding = {
  code: '_ActPharmacySupplyType',
  display: 'ActPharmacySupplyType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActPolicyType: Coding = {
  code: '_ActPolicyType',
  display: 'ActPolicyType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActPrivacyLaw: Coding = {
  code: '_ActPrivacyLaw',
  display: 'ActPrivacyLaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActPrivacyPolicy: Coding = {
  code: '_ActPrivacyPolicy',
  display: 'ActPrivacyPolicy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActProcedureCode: Coding = {
  code: '_ActProcedureCode',
  display: 'ActProcedureCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActProductAcquisitionCode: Coding = {
  code: '_ActProductAcquisitionCode',
  display: 'ActProductAcquisitionCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActProgramTypeCode: Coding = {
  code: '_ActProgramTypeCode',
  display: 'ActProgramTypeCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActSpecimenTransportCode: Coding = {
  code: '_ActSpecimenTransportCode',
  display: 'ActSpecimenTransportCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActSpecimenTreatmentCode: Coding = {
  code: '_ActSpecimenTreatmentCode',
  display: 'ActSpecimenTreatmentCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActSpecObsCode: Coding = {
  code: '_ActSpecObsCode',
  display: 'ActSpecObsCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActSubstanceAdministrationCode: Coding = {
  code: '_ActSubstanceAdministrationCode',
  display: 'ActSubstanceAdministrationCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActTaskCode: Coding = {
  code: '_ActTaskCode',
  display: 'ActTaskCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActTherapyDurationWorkingListCode: Coding = {
  code: '_ActTherapyDurationWorkingListCode',
  display: 'ActTherapyDurationWorkingListCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActTransportationModeCode: Coding = {
  code: '_ActTransportationModeCode',
  display: 'ActTransportationModeCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActUSPrivacyLaw: Coding = {
  code: '_ActUSPrivacyLaw',
  display: '_ActUSPrivacyLaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AnnotationType: Coding = {
  code: '_AnnotationType',
  display: 'AnnotationType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CaseTransmissionMode: Coding = {
  code: '_CaseTransmissionMode',
  display: 'case transmission mode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ECGObservationSeriesType: Coding = {
  code: '_ECGObservationSeriesType',
  display: 'ECGObservationSeriesType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EntityInformationSensitivityPolicy: Coding = {
  code: '_EntitySensitivityPolicyType',
  display: 'EntityInformationSensitivityPolicy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FDALabelData: Coding = {
  code: '_FDALabelData',
  display: 'FDALabelData',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GeneticObservationType: Coding = {
  code: '_GeneticObservationType',
  display: 'GeneticObservationType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HL7AccommodationCode: Coding = {
  code: '_HL7AccommodationCode',
  display: 'HL7AccommodationCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HL7DefinedActCodes: Coding = {
  code: '_HL7DefinedActCodes',
  display: 'HL7DefinedActCodes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ImmunizationObservationType: Coding = {
  code: '_ImmunizationObservationType',
  display: 'ImmunizationObservationType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_IndividualCaseSafetyReportType: Coding = {
  code: '_IndividualCaseSafetyReportType',
  display: 'Individual Case Safety Report Type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InformationSensitivityPolicy: Coding = {
  code: '_InformationSensitivityPolicy',
  display: 'InformationSensitivityPolicy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InvoiceElementAdjudicated: Coding = {
  code: '_InvoiceElementAdjudicated',
  display: 'InvoiceElementAdjudicated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InvoiceElementPaid: Coding = {
  code: '_InvoiceElementPaid',
  display: 'InvoiceElementPaid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InvoiceElementSubmitted: Coding = {
  code: '_InvoiceElementSubmitted',
  display: 'InvoiceElementSubmitted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LOINCObservationActContextAgeType: Coding = {
  code: '_LOINCObservationActContextAgeType',
  display: 'LOINCObservationActContextAgeType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MedicationObservationType: Coding = {
  code: '_MedicationObservationType',
  display: 'MedicationObservationType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ObservationIssueTriggerCodedObservationType: Coding = {
  code: '_ObservationIssueTriggerCodedObservationType',
  display: 'ObservationIssueTriggerCodedObservationType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ObservationQualityMeasureAttribute: Coding = {
  code: '_ObservationQualityMeasureAttribute',
  display: 'ObservationQualityMeasureAttribute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ObservationSequenceType: Coding = {
  code: '_ObservationSequenceType',
  display: 'ObservationSequenceType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ObservationSeriesType: Coding = {
  code: '_ObservationSeriesType',
  display: 'ObservationSeriesType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ObservationType: Coding = {
  code: '_ObservationType',
  display: 'ObservationType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PatientImmunizationRelatedObservationType: Coding = {
  code: '_PatientImmunizationRelatedObservationType',
  display: 'PatientImmunizationRelatedObservationType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PopulationInclusionObservationType: Coding = {
  code: '_PopulationInclusionObservationType',
  display: 'PopulationInclusionObservationType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PreferenceObservationType: Coding = {
  code: '_PreferenceObservationType',
  display: '_PreferenceObservationType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ROIOverlayShape: Coding = {
  code: '_ROIOverlayShape',
  display: 'ROIOverlayShape',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RoleInformationSensitivityPolicy: Coding = {
  code: '_RoleInformationSensitivityPolicy',
  display: 'RoleInformationSensitivityPolicy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AgePatientQnEst: Coding = {
  code: '21611-9',
  display: 'age patient qn est',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AgePatientQnReported: Coding = {
  code: '21612-7',
  display: 'age patient qn reported',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AgePatientQnCalc: Coding = {
  code: '29553-5',
  display: 'age patient qn calc',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AgePatientQnDefinition: Coding = {
  code: '30525-0',
  display: 'age patient qn definition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AgeAtOnsetOfAdverseEvent: Coding = {
  code: '30972-4',
  display: 'age at onset of adverse event',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_VAL42CFRPart2: Coding = {
  code: '42CFRPart2',
  display: '42 CFR Part2',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudicatedWithAdjustments: Coding = {
  code: 'AA',
  display: 'adjudicated with adjustments',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccreditedAssistedLivingCare: Coding = {
  code: 'AALC',
  display: 'accredited assisted living care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccreditedAmbulatoryCare: Coding = {
  code: 'AAMC',
  display: 'accredited ambulatory care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccreditedBehavioralHealthCare: Coding = {
  code: 'ABHC',
  display: 'accredited behavioral health care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CommonlyAbusedMisusedAlert: Coding = {
  code: 'ABUSE',
  display: 'commonly abused/misused alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccreditedCriticalAccessHospitalCare: Coding = {
  code: 'ACAC',
  display: 'accredited critical access hospital care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdverseDrugReactionAccess: Coding = {
  code: 'ACADR',
  display: 'adverse drug reaction access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AllAccess: Coding = {
  code: 'ACALL',
  display: 'all access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AllergyAccess: Coding = {
  code: 'ACALLG',
  display: 'allergy access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccessControlScheme: Coding = {
  code: 'ACCESSCONSCHEME',
  display: 'access control scheme',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InformationalConsentAccess: Coding = {
  code: 'ACCONS',
  display: 'informational consent access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DemographicsAccess: Coding = {
  code: 'ACDEMO',
  display: 'demographics access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DiagnosticImagingAccess: Coding = {
  code: 'ACDI',
  display: 'diagnostic imaging access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AutomatedClearingHouse: Coding = {
  code: 'ACH',
  display: 'Automated Clearing House',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccreditedHospitalCare: Coding = {
  code: 'ACHC',
  display: 'accredited hospital care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Acidification: Coding = {
  code: 'ACID',
  display: 'Acidification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ImmunizationAccess: Coding = {
  code: 'ACIMMUN',
  display: 'immunization access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LabTestResultAccess: Coding = {
  code: 'ACLAB',
  display: 'lab test result access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MedicationAccess: Coding = {
  code: 'ACMED',
  display: 'medication access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MedicalConditionAccess: Coding = {
  code: 'ACMEDC',
  display: 'medical condition access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MentalHealthAccess: Coding = {
  code: 'ACMEN',
  display: 'mental health access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CommonObservationsAccess: Coding = {
  code: 'ACOBS',
  display: 'common observations access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccountableCareOrganizationCompartment: Coding = {
  code: 'ACOCOMPT',
  display: 'accountable care organization compartment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PolicyOrProgramInformationAccess: Coding = {
  code: 'ACPOLPRG',
  display: 'policy or program information access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ProviderInformationAccess: Coding = {
  code: 'ACPROV',
  display: 'provider information access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ProfessionalServiceAccess: Coding = {
  code: 'ACPSERV',
  display: 'professional service access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubstanceAbuseAccess: Coding = {
  code: 'ACSUBSTAB',
  display: 'substance abuse access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrustPolicy: Coding = {
  code: 'ActTrustPolicyType',
  display: 'trust policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ShortTermAcute: Coding = {
  code: 'ACU',
  display: 'short term/acute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InpatientAcute: Coding = {
  code: 'ACUTE',
  display: 'inpatient acute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdultAlert: Coding = {
  code: 'ADALRT',
  display: 'adult alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdmittingDiagnosis: Coding = {
  code: 'ADMDX',
  display: 'admitting diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNullifiedPriorPeriodElectronicAmount: Coding = {
  code: 'ADNFPPELAT',
  display: 'adjud. nullified prior-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNullifiedPriorPeriodElectronicCount: Coding = {
  code: 'ADNFPPELCT',
  display: 'adjud. nullified prior-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNullifiedPriorPeriodManualAmount: Coding = {
  code: 'ADNFPPMNAT',
  display: 'adjud. nullified prior-period manual amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNullifiedPriorPeriodManualCount: Coding = {
  code: 'ADNFPPMNCT',
  display: 'adjud. nullified prior-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNullifiedSamePeriodElectronicAmount: Coding = {
  code: 'ADNFSPELAT',
  display: 'adjud. nullified same-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNullifiedSamePeriodElectronicCount: Coding = {
  code: 'ADNFSPELCT',
  display: 'adjud. nullified same-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNullifiedSamePeriodManualAmount: Coding = {
  code: 'ADNFSPMNAT',
  display: 'adjud. nullified same-period manual amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNullifiedSamePeriodManualCount: Coding = {
  code: 'ADNFSPMNCT',
  display: 'adjud. nullified same-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNonPayeePayablePriorPeriodElectronicAmount: Coding = {
  code: 'ADNPPPELAT',
  display: 'adjud. non-payee payable prior-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNonPayeePayablePriorPeriodElectronicCount: Coding = {
  code: 'ADNPPPELCT',
  display: 'adjud. non-payee payable prior-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNonPayeePayablePriorPeriodManualAmount: Coding = {
  code: 'ADNPPPMNAT',
  display: 'adjud. non-payee payable prior-period manual amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNonPayeePayablePriorPeriodManualCount: Coding = {
  code: 'ADNPPPMNCT',
  display: 'adjud. non-payee payable prior-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNonPayeePayableSamePeriodElectronicAmount: Coding = {
  code: 'ADNPSPELAT',
  display: 'adjud. non-payee payable same-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNonPayeePayableSamePeriodElectronicCount: Coding = {
  code: 'ADNPSPELCT',
  display: 'adjud. non-payee payable same-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNonPayeePayableSamePeriodManualAmount: Coding = {
  code: 'ADNPSPMNAT',
  display: 'adjud. non-payee payable same-period manual amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudNonPayeePayableSamePeriodManualCount: Coding = {
  code: 'ADNPSPMNCT',
  display: 'adjud. non-payee payable same-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdolescentInformationSensitivity: Coding = {
  code: 'ADOL',
  display: 'adolescent information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudPayeePayablePriorPeriodElectronicAmount: Coding = {
  code: 'ADPPPPELAT',
  display: 'adjud. payee payable prior-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudPayeePayablePriorPeriodElectronicCount: Coding = {
  code: 'ADPPPPELCT',
  display: 'adjud. payee payable prior-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudPayeePayablePriorPeriodManualAmout: Coding = {
  code: 'ADPPPPMNAT',
  display: 'adjud. payee payable prior-period manual amout',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudPayeePayablePriorPeriodManualCount: Coding = {
  code: 'ADPPPPMNCT',
  display: 'adjud. payee payable prior-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudPayeePayableSamePeriodElectronicAmount: Coding = {
  code: 'ADPPSPELAT',
  display: 'adjud. payee payable same-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudPayeePayableSamePeriodElectronicCount: Coding = {
  code: 'ADPPSPELCT',
  display: 'adjud. payee payable same-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudPayeePayableSamePeriodManualAmount: Coding = {
  code: 'ADPPSPMNAT',
  display: 'adjud. payee payable same-period manual amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudPayeePayableSamePeriodManualCount: Coding = {
  code: 'ADPPSPMNCT',
  display: 'adjud. payee payable same-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudRefusedPriorPeriodElectronicAmount: Coding = {
  code: 'ADRFPPELAT',
  display: 'adjud. refused prior-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudRefusedPriorPeriodElectronicCount: Coding = {
  code: 'ADRFPPELCT',
  display: 'adjud. refused prior-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudRefusedPriorPeriodManualAmount: Coding = {
  code: 'ADRFPPMNAT',
  display: 'adjud. refused prior-period manual amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudRefusedPriorPeriodManualCount: Coding = {
  code: 'ADRFPPMNCT',
  display: 'adjud. refused prior-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudRefusedSamePeriodElectronicAmount: Coding = {
  code: 'ADRFSPELAT',
  display: 'adjud. refused same-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudRefusedSamePeriodElectronicCount: Coding = {
  code: 'ADRFSPELCT',
  display: 'adjud. refused same-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudRefusedSamePeriodManualAmount: Coding = {
  code: 'ADRFSPMNAT',
  display: 'adjud. refused same-period manual amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudRefusedSamePeriodManualCount: Coding = {
  code: 'ADRFSPMNCT',
  display: 'adjud. refused same-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdverseReaction: Coding = {
  code: 'ADVERSE_REACTION',
  display: 'Adverse Reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PedestrianTransport: Coding = {
  code: 'AFOOT',
  display: 'pedestrian transport',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NonNormalHours: Coding = {
  code: 'AFTHRS',
  display: 'non-normal hours',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AgeAlert: Coding = {
  code: 'AGE',
  display: 'Age Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AggregateMeasureObservation: Coding = {
  code: 'AGGREGATE',
  display: 'aggregate measure observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccreditedHomeCare: Coding = {
  code: 'AHOC',
  display: 'accredited home care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AirborneTransmission: Coding = {
  code: 'AIRTRNS',
  display: 'airborne transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AlternativeLevelOfCare: Coding = {
  code: 'ALC',
  display: 'Alternative Level of Care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AlternateElectronic: Coding = {
  code: 'ALEC',
  display: 'alternate electronic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Allergy: Coding = {
  code: 'ALG',
  display: 'Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AllergyAlert: Coding = {
  code: 'ALGY',
  display: 'Allergy Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Alkalization: Coding = {
  code: 'ALK',
  display: 'Alkalization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AllCategories: Coding = {
  code: 'ALLCAT',
  display: 'all categories',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AllergyListEntry: Coding = {
  code: 'ALLERLE',
  display: 'allergy list entry',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AllergyListReview: Coding = {
  code: 'ALLERLREV',
  display: 'allergy list review',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AllergyCategory: Coding = {
  code: 'ALLGCAT',
  display: 'allergy category',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccreditedLongTermCare: Coding = {
  code: 'ALTC',
  display: 'accredited long term care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Ambulatory: Coding = {
  code: 'AMB',
  display: 'ambulatory',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FixedWingAmbulanceTransport: Coding = {
  code: 'AMBAIR',
  display: 'fixed-wing ambulance transport',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GroundAmbulanceTransport: Coding = {
  code: 'AMBGRND',
  display: 'ground ambulance transport',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HelicopterAmbulanceTransport: Coding = {
  code: 'AMBHELO',
  display: 'helicopter ambulance transport',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AmbulanceTransport: Coding = {
  code: 'AMBT',
  display: 'ambulance transport',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AnimalToAnimalTransmission: Coding = {
  code: 'ANANTRNS',
  display: 'animal to animal transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudicatedWithAdjustmentsAndNoFinancialImpact: Coding = {
  code: 'ANF',
  display: 'adjudicated with adjustments and no financial impact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AnimalToHumanTransmission: Coding = {
  code: 'ANHUMTRNS',
  display: 'animal to human transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DiagnosticImageNote: Coding = {
  code: 'ANNDI',
  display: 'diagnostic image note',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GeneralNote: Coding = {
  code: 'ANNGEN',
  display: 'general note',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ImmunizationNote: Coding = {
  code: 'ANNIMM',
  display: 'immunization note',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LaboratoryNote: Coding = {
  code: 'ANNLAB',
  display: 'laboratory note',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MedicationNote: Coding = {
  code: 'ANNMED',
  display: 'medication note',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AnnuityPolicy: Coding = {
  code: 'ANNU',
  display: 'annuity policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccountingOfDisclosure: Coding = {
  code: 'AOD',
  display: 'accounting of disclosure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccreditedOfficeBasedSurgeryCare: Coding = {
  code: 'AOSC',
  display: 'accredited office-based surgery care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudicatedAsRefused: Coding = {
  code: 'AR',
  display: 'adjudicated as refused',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdverseDrugReactionCategory: Coding = {
  code: 'ARCAT',
  display: 'adverse drug reaction category',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActSpecObsArtBldCode: Coding = {
  code: 'ARTBLD',
  display: 'ActSpecObsArtBldCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AdjudicatedAsSubmitted: Coding = {
  code: 'AS',
  display: 'adjudicated as submitted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Assertion: Coding = {
  code: 'ASSERTION',
  display: 'Assertion',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Audit: Coding = {
  code: 'AUDIT',
  display: 'audit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AuditTrail: Coding = {
  code: 'AUDTR',
  display: 'audit trail',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Authorized: Coding = {
  code: 'AUTH',
  display: 'Authorized',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AuthorizationPolicy: Coding = {
  code: 'AUTHPOL',
  display: 'authorization policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AutoRepeatPermission: Coding = {
  code: 'AUTO',
  display: 'auto-repeat permission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AutoAttachment: Coding = {
  code: 'AUTOATTCH',
  display: 'auto attachment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AutoHighDilution: Coding = {
  code: 'AUTO-HIGH',
  display: 'Auto-High Dilution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AutoLowDilution: Coding = {
  code: 'AUTO-LOW',
  display: 'Auto-Low Dilution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Automobile: Coding = {
  code: 'AUTOPOL',
  display: 'automobile',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AvailableVolume: Coding = {
  code: 'AVAILABLE',
  display: 'Available Volume',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_BusinessInformationSensitivity: Coding = {
  code: 'B',
  display: 'business information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_BodyFluidContactTransmission: Coding = {
  code: 'BDYFLDTRNS',
  display: 'body fluid contact transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_BehavioralHealthInformationSensitivity: Coding = {
  code: 'BH',
  display: 'behavioral health information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_BloodBorneTransmission: Coding = {
  code: 'BLDTRNS',
  display: 'blood borne transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_BlockFunding: Coding = {
  code: 'BLK',
  display: 'block funding',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Bonus: Coding = {
  code: 'BONUS',
  display: 'bonus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_BoosterImmunization: Coding = {
  code: 'BOOSTER',
  display: 'Booster Immunization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_BreikostGE: Coding = {
  code: 'BR',
  display: 'breikost (GE)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_BusinessConstraintViolation: Coding = {
  code: 'BUS',
  display: 'business constraint violation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Corrected: Coding = {
  code: 'C',
  display: 'corrected',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedAnatomicPathologyAndClinicalPathologyCare: Coding = {
  code: 'CACC',
  display: 'certified anatomic pathology and clinical pathology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedAcuteCoronarySyndromeCare: Coding = {
  code: 'CACS',
  display: 'certified acute coronary syndrome care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedAllergyAndImmunologyCare: Coding = {
  code: 'CAIC',
  display: 'certified allergy and immunology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedAerospaceMedicineCare: Coding = {
  code: 'CAMC',
  display: 'certified aerospace medicine care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedAcuteMyocardialInfarctionCare: Coding = {
  code: 'CAMI',
  display: 'certified acute myocardial infarction care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedAnesthesiologyCare: Coding = {
  code: 'CANC',
  display: 'certified anesthesiology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CancelledAppointment: Coding = {
  code: 'CANCAPT',
  display: 'cancelled appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_WomenQuoteSCancerDetectionProgram: Coding = {
  code: 'CANPRG',
  display: "women's cancer detection program",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CapitationFunding: Coding = {
  code: 'CAP',
  display: 'capitation funding',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedAnatomicPathologyCare: Coding = {
  code: 'CAPC',
  display: 'certified anatomic pathology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Cardiology: Coding = {
  code: 'CARD',
  display: 'Cardiology',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CarePlan: Coding = {
  code: 'CARELIST',
  display: 'care plan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CaseSeriousnessCriteria: Coding = {
  code: 'CASESER',
  display: 'case seriousness criteria',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedAsthmaCare: Coding = {
  code: 'CAST',
  display: 'certified asthma care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedBariatricSurgeryCare: Coding = {
  code: 'CBAR',
  display: 'certified bariatric surgery care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedClinicalBiochemicalGeneticsCare: Coding = {
  code: 'CBGC',
  display: 'certified clinical biochemical genetics care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedCoronaryArteryDiseaseCare: Coding = {
  code: 'CCAD',
  display: 'certified coronary artery disease care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedCardiacCare: Coding = {
  code: 'CCAR',
  display: 'certified cardiac care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedClinicalCytogeneticsCare: Coding = {
  code: 'CCCC',
  display: 'certified clinical cytogenetics care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedClinicalGeneticsMDCare: Coding = {
  code: 'CCGC',
  display: 'certified clinical genetics (M.D.) care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedClinicalPathologyCare: Coding = {
  code: 'CCPC',
  display: 'certified clinical pathology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedColonAndRectalSurgeryCare: Coding = {
  code: 'CCSC',
  display: 'certified colon and rectal surgery care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedDermatologyCare: Coding = {
  code: 'CDEC',
  display: 'certified dermatology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedDepressionCare: Coding = {
  code: 'CDEP',
  display: 'certified depression care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedDigestiveGastrointestinalDisordersCare: Coding = {
  code: 'CDGD',
  display: 'certified digestive/gastrointestinal disorders care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedDiabetesCare: Coding = {
  code: 'CDIA',
  display: 'certified diabetes care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CaseDiseaseImportedObservation: Coding = {
  code: 'CDIO',
  display: 'case disease imported observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedDiagnosticRadiologyCare: Coding = {
  code: 'CDRC',
  display: 'certified diagnostic radiology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ClinicalDecisionSupportInterventionReview: Coding = {
  code: 'CDSREV',
  display: 'clinical decision support intervention review',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CelebrityInformationSensitivity: Coding = {
  code: 'CEL',
  display: 'celebrity information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedEmergencyMedicineCare: Coding = {
  code: 'CEMC',
  display: 'certified emergency medicine care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedEpilepsyCare: Coding = {
  code: 'CEPI',
  display: 'certified epilepsy care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedFrailElderlyCare: Coding = {
  code: 'CFEL',
  display: 'certified frail elderly care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedFamilyPracticeCare: Coding = {
  code: 'CFPC',
  display: 'certified family practice care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CarryForwardAdjusment: Coding = {
  code: 'CFWD',
  display: 'carry forward adjusment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CharityProgram: Coding = {
  code: 'CHAR',
  display: 'charity program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedHeartFailureCare: Coding = {
  code: 'CHFC',
  display: 'certified heart failure care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Cheque: Coding = {
  code: 'CHK',
  display: 'Cheque',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DayCareChildCareInteraction: Coding = {
  code: 'CHLDCARE',
  display: 'Day care - Child care Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Chronic: Coding = {
  code: 'CHR',
  display: 'Chronic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_StandardCharge: Coding = {
  code: 'CHRG',
  display: 'Standard Charge',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedHighRiskObstetricsCare: Coding = {
  code: 'CHRO',
  display: 'certified high risk obstetrics care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ContinuousChronic: Coding = {
  code: 'CHRON',
  display: 'continuous/chronic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedHyperlipidemiaCare: Coding = {
  code: 'CHYP',
  display: 'certified hyperlipidemia care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedInternalMedicineCare: Coding = {
  code: 'CIMC',
  display: 'certified internal medicine care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Circle: Coding = {
  code: 'CIRCLE',
  display: 'circle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ClinicalNoteEntryTask: Coding = {
  code: 'CLINNOTEE',
  display: 'clinical note entry task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ClinicalNoteReviewTask: Coding = {
  code: 'CLINNOTEREV',
  display: 'clinical note review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Classroom: Coding = {
  code: 'CLSSRM',
  display: 'classroom',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedClinicalMolecularGeneticsCare: Coding = {
  code: 'CMGC',
  display: 'certified clinical molecular genetics care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedMigraineHeadacheCare: Coding = {
  code: 'CMIH',
  display: 'certified migraine headache care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CompositeMeasureMethod: Coding = {
  code: 'CMPMSRMTH',
  display: 'composite measure method',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ComponentMeasureScoringWeight: Coding = {
  code: 'CMPMSRSCRWGHT',
  display: 'component measure scoring weight',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedMultipleSclerosisCare: Coding = {
  code: 'CMSC',
  display: 'certified multiple sclerosis care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedNeurologyCare: Coding = {
  code: 'CNEC',
  display: 'certified neurology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedNuclearMedicineCare: Coding = {
  code: 'CNMC',
  display: 'certified nuclear medicine care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedNeurologyWithSpecialQualificationsInChildNeurologyCare: Coding = {
  code: 'CNQC',
  display: 'certified neurology with special qualifications in child neurology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedNeurologicalSurgeryCare: Coding = {
  code: 'CNSC',
  display: 'certified neurological surgery care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CommonObservationCategory: Coding = {
  code: 'COBSCAT',
  display: 'common observation category',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CodeHasBeenDeprecated: Coding = {
  code: 'CODE_DEPREC',
  display: 'code has been deprecated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CodeIsNotValid: Coding = {
  code: 'CODE_INVAL',
  display: 'code is not valid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedObstetricsAndGynecologyCare: Coding = {
  code: 'COGC',
  display: 'certified obstetrics and gynecology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CognitiveDisabilityInformationSensitivity: Coding = {
  code: 'COGN',
  display: 'cognitive disability information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Coinsurance: Coding = {
  code: 'COIN',
  display: 'coinsurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CoInsurance: Coding = {
  code: 'COINS',
  display: 'co-insurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedOrthopedicJointReplacementCare: Coding = {
  code: 'COJR',
  display: 'certified orthopedic joint replacement care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CollisionCoveragePolicy: Coding = {
  code: 'COL',
  display: 'collision coverage policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedOccupationalMedicineCare: Coding = {
  code: 'COMC',
  display: 'certified occupational medicine care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CommonRule: Coding = {
  code: 'CommonRule',
  display: 'Common Rule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ComplianceAlert: Coding = {
  code: 'COMPLY',
  display: 'Compliance Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Compartment: Coding = {
  code: 'COMPT',
  display: 'compartment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedOncologyCare: Coding = {
  code: 'CONC',
  display: 'certified oncology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ConditionAlert: Coding = {
  code: 'COND',
  display: 'Condition Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ConditionList: Coding = {
  code: 'CONDLIST',
  display: 'condition list',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ConsumptionVolume: Coding = {
  code: 'CONSUMPTION',
  display: 'Consumption Volume',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Contract: Coding = {
  code: 'CONT',
  display: 'contract',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ContractFunding: Coding = {
  code: 'CONTF',
  display: 'contract funding',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CommonConveyanceInteraction: Coding = {
  code: 'CONVEYNC',
  display: 'Common Conveyance Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_COPAY: Coding = {
  code: 'COPAY',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PatientCoPay: Coding = {
  code: 'COPAYMENT',
  display: 'patient co-pay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedOphthalmologyCare: Coding = {
  code: 'COPC',
  display: 'certified ophthalmology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedChronicObstructivePulmonaryDiseaseCare: Coding = {
  code: 'COPD',
  display: 'certified chronic obstructive pulmonary disease care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Copyright: Coding = {
  code: 'COPY',
  display: 'copyright',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedOrganTransplantCare: Coding = {
  code: 'CORT',
  display: 'certified organ transplant care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedOrthopaedicSurgeryCare: Coding = {
  code: 'COSC',
  display: 'certified orthopaedic surgery care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedOtolaryngologyCare: Coding = {
  code: 'COTC',
  display: 'certified otolaryngology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CoverageProblem: Coding = {
  code: 'COVGE',
  display: 'coverage problem',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CoverageMaximum: Coding = {
  code: 'COVMX',
  display: 'coverage maximum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_BenefitPolicy: Coding = {
  code: 'COVPOL',
  display: 'benefit policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CoveragePeriod: Coding = {
  code: 'COVPRD',
  display: 'coverage period',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedParkinsonsDiseaseCare: Coding = {
  code: 'CPAD',
  display: 'certified parkinsons disease care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedPediatricsCare: Coding = {
  code: 'CPEC',
  display: 'certified pediatrics care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedPhDMedicalGeneticsCare: Coding = {
  code: 'CPGC',
  display: 'certified Ph.D. medical genetics care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedPublicHealthAndGeneralPreventiveMedicineCare: Coding = {
  code: 'CPHC',
  display: 'certified public health and general preventive medicine care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ClinicalProductInvoice: Coding = {
  code: 'CPINV',
  display: 'clinical product invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ComplyWithConfidentialityCode: Coding = {
  code: 'CPLYCC',
  display: 'comply with confidentiality code',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ComplyWithConsentDirective: Coding = {
  code: 'CPLYCD',
  display: 'comply with consent directive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ComplyWithJurisdictionalPrivacyPolicy: Coding = {
  code: 'CPLYJPP',
  display: 'comply with jurisdictional privacy policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ComplyWithOrganizationalPrivacyPolicy: Coding = {
  code: 'CPLYOPP',
  display: 'comply with organizational privacy policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ComplyWithOrganizationalSecurityPolicy: Coding = {
  code: 'CPLYOSP',
  display: 'comply with organizational security policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ComplyWithPolicy: Coding = {
  code: 'CPLYPOL',
  display: 'comply with policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedPneumoniaDiseaseCare: Coding = {
  code: 'CPND',
  display: 'certified pneumonia disease care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CompoundDrugInvoiceGroup: Coding = {
  code: 'CPNDDRGING',
  display: 'compound drug invoice group',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CompoundIngredientInvoiceGroup: Coding = {
  code: 'CPNDINDING',
  display: 'compound ingredient invoice group',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CompoundSupplyInvoiceGroup: Coding = {
  code: 'CPNDSUPING',
  display: 'compound supply invoice group',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedPhysicalMedicineAndRehabilitationCare: Coding = {
  code: 'CPRC',
  display: 'certified physical medicine and rehabilitation care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedPlasticSurgeryCare: Coding = {
  code: 'CPSC',
  display: 'certified plastic surgery care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedPrimaryStrokeCenterCare: Coding = {
  code: 'CPST',
  display: 'certified primary stroke center care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CPTModifierCodes: Coding = {
  code: 'CPTM',
  display: 'CPT modifier codes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedPsychiatryCare: Coding = {
  code: 'CPYC',
  display: 'certified psychiatry care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CommonReactionAlert: Coding = {
  code: 'CREACT',
  display: 'common reaction alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CrimeVictimProgram: Coding = {
  code: 'CRIME',
  display: 'crime victim program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Criticality: Coding = {
  code: 'CRIT',
  display: 'criticality',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedRadiationOncologyCare: Coding = {
  code: 'CROC',
  display: 'certified radiation oncology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedRadiologicalPhysicsCare: Coding = {
  code: 'CRPC',
  display: 'certified radiological physics care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ClinicalRecommendationStatement: Coding = {
  code: 'CRS',
  display: 'clinical recommendation statement',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedStrokeDiseaseManagementCare: Coding = {
  code: 'CSDM',
  display: 'certified stroke disease management care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedSickleCellCare: Coding = {
  code: 'CSIC',
  display: 'certified sickle cell care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ClinicalServiceInvoice: Coding = {
  code: 'CSINV',
  display: 'clinical service invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedSleepDisordersCare: Coding = {
  code: 'CSLD',
  display: 'certified sleep disorders care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ClinicalServiceAndProduct: Coding = {
  code: 'CSPINV',
  display: 'clinical service and product',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedSpineTreatmentCare: Coding = {
  code: 'CSPT',
  display: 'certified spine treatment care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedSurgeryCare: Coding = {
  code: 'CSUC',
  display: 'certified surgery care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedTraumaBurnCenterCare: Coding = {
  code: 'CTBU',
  display: 'certified trauma/burn center care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CareTeamCompartment: Coding = {
  code: 'CTCOMPT',
  display: 'care team compartment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ControlledSubstance: Coding = {
  code: 'CTLSUB',
  display: 'Controlled Substance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CaseTransmissionModeObservation: Coding = {
  code: 'CTMO',
  display: 'case transmission mode observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedThoracicSurgeryCare: Coding = {
  code: 'CTSC',
  display: 'certified thoracic surgery care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedUrologyCare: Coding = {
  code: 'CURC',
  display: 'certified urology care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CurrentMedicationList: Coding = {
  code: 'CURMEDLIST',
  display: 'current medication list',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CurrentVolume: Coding = {
  code: 'CURRENT',
  display: 'Current Volume',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedVascularDiseasesCare: Coding = {
  code: 'CVDC',
  display: 'certified vascular diseases care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedVascularSurgeryCare: Coding = {
  code: 'CVSC',
  display: 'certified vascular surgery care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedWoundManagementCare: Coding = {
  code: 'CWMA',
  display: 'certified wound management care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CertifiedWomenQuoteSHealthCare: Coding = {
  code: 'CWOH',
  display: "certified women's health care",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DrugAllergy: Coding = {
  code: 'DALG',
  display: 'Drug Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Day: Coding = {
  code: 'DAY',
  display: 'day',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DirectDeposit: Coding = {
  code: 'DDP',
  display: 'Direct Deposit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DeclassifySecurityLabel: Coding = {
  code: 'DECLASSIFYLABEL',
  display: 'declassify security label',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DEDUCT: Coding = {
  code: 'DEDUCT',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Deductible: Coding = {
  code: 'DEDUCTIBLE',
  display: 'deductible',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Definition: Coding = {
  code: 'DEF',
  display: 'definition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Defibrination: Coding = {
  code: 'DEFB',
  display: 'Defibrination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DeleteAfterUse: Coding = {
  code: 'DELAU',
  display: 'delete after use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DelegationPolicy: Coding = {
  code: 'DELEPOL',
  display: 'delegation policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AllDemographicInformationSensitivity: Coding = {
  code: 'DEMO',
  display: 'all demographic information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DemographicsCategory: Coding = {
  code: 'DEMOCAT',
  display: 'demographics category',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DenominatorExclusions: Coding = {
  code: 'DENEX',
  display: 'denominator exclusions',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DenominatorExceptions: Coding = {
  code: 'DENEXCEP',
  display: 'denominator exceptions',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Denominator: Coding = {
  code: 'DENOM',
  display: 'denominator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DentalCarePolicy: Coding = {
  code: 'DENTAL',
  display: 'dental care policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DentalProgram: Coding = {
  code: 'DENTPRG',
  display: 'dental program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TransdermalTransmission: Coding = {
  code: 'DERMTRNS',
  display: 'transdermal transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DailyFill: Coding = {
  code: 'DF',
  display: 'Daily Fill',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DiagnosisInformationSensitivity: Coding = {
  code: 'DIA',
  display: 'diagnosis information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DiagnosisListEntryTask: Coding = {
  code: 'DIAGLISTE',
  display: 'diagnosis list entry task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DiagnosisListReviewTask: Coding = {
  code: 'DIAGLISTREV',
  display: 'diagnosis list review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DiagnosticImageCategory: Coding = {
  code: 'DICAT',
  display: 'diagnostic image category',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Diet: Coding = {
  code: 'DIET',
  display: 'Diet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActSpecObsDilutionCode: Coding = {
  code: 'DILUTION',
  display: 'ActSpecObsDilutionCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DrugIntolerance: Coding = {
  code: 'DINT',
  display: 'Drug Intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DisabilityInsurancePolicy: Coding = {
  code: 'DIS',
  display: 'disability insurance policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Disclaimer: Coding = {
  code: 'DISC',
  display: 'disclaimer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DischargeInstructionEntry: Coding = {
  code: 'DISCHINSTE',
  display: 'discharge instruction entry',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DischargeSummaryEntryTask: Coding = {
  code: 'DISCHSUME',
  display: 'discharge summary entry task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DischargeSummaryReviewTask: Coding = {
  code: 'DISCHSUMREV',
  display: 'discharge summary review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DischargeMedicationList: Coding = {
  code: 'DISCMEDLIST',
  display: 'discharge medication list',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DischargeDiagnosis: Coding = {
  code: 'DISDX',
  display: 'discharge diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DiseaseSpecificPolicy: Coding = {
  code: 'DISEASE',
  display: 'disease specific policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PublicHealthProgram: Coding = {
  code: 'DISEASEPRG',
  display: 'public health program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Display: Coding = {
  code: 'DISPLAY',
  display: 'Display',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DiabetesMellitusDiet: Coding = {
  code: 'DM',
  display: 'diabetes mellitus diet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DrugNonAllergyIntolerance: Coding = {
  code: 'DNAINT',
  display: 'Drug Non-Allergy Intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Dental: Coding = {
  code: 'DNTL',
  display: 'Dental',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DateOfBirthInformationSensitivity: Coding = {
  code: 'DOB',
  display: 'date of birth information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Document: Coding = {
  code: 'DOCUMENT',
  display: 'document',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DosageProblem: Coding = {
  code: 'DOSE',
  display: 'Dosage problem',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DosageConditionAlert: Coding = {
  code: 'DOSECOND',
  display: 'dosage-condition alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DoseDurationAlert: Coding = {
  code: 'DOSEDUR',
  display: 'Dose-Duration Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DoseDurationHighAlert: Coding = {
  code: 'DOSEDURH',
  display: 'Dose-Duration High Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DoseDurationHighForIndicationAlert: Coding = {
  code: 'DOSEDURHIND',
  display: 'Dose-Duration High for Indication Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DoseDurationLowAlert: Coding = {
  code: 'DOSEDURL',
  display: 'Dose-Duration Low Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DoseDurationLowForIndicationAlert: Coding = {
  code: 'DOSEDURLIND',
  display: 'Dose-Duration Low for Indication Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HighDoseAlert: Coding = {
  code: 'DOSEH',
  display: 'High Dose Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HighDoseForIndicationAlert: Coding = {
  code: 'DOSEHIND',
  display: 'High Dose for Indication Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HighDoseForAgeAlert: Coding = {
  code: 'DOSEHINDA',
  display: 'High Dose for Age Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HighDoseForHeightSurfaceAreaAlert: Coding = {
  code: 'DOSEHINDSA',
  display: 'High Dose for Height/Surface Area Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HighDoseForWeightAlert: Coding = {
  code: 'DOSEHINDW',
  display: 'High Dose for Weight Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DOSEIND: Coding = {
  code: 'DOSEIND',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DoseIntervalAlert: Coding = {
  code: 'DOSEIVL',
  display: 'Dose-Interval Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DoseIntervalForIndicationAlert: Coding = {
  code: 'DOSEIVLIND',
  display: 'Dose-Interval for Indication Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LowDoseAlert: Coding = {
  code: 'DOSEL',
  display: 'Low Dose Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LowDoseForIndicationAlert: Coding = {
  code: 'DOSELIND',
  display: 'Low Dose for Indication Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LowDoseForAgeAlert: Coding = {
  code: 'DOSELINDA',
  display: 'Low Dose for Age Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LowDoseForHeightSurfaceAreaAlert: Coding = {
  code: 'DOSELINDSA',
  display: 'Low Dose for Height/Surface Area Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LowDoseForWeightAlert: Coding = {
  code: 'DOSELINDW',
  display: 'Low Dose for Weight Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DowngradeSecurityLabel: Coding = {
  code: 'DOWNGRDLABEL',
  display: 'downgrade security label',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DrugInformationSensitivity: Coding = {
  code: 'DRGIS',
  display: 'drug information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DrugRehab: Coding = {
  code: 'DRGRHB',
  display: 'Drug Rehab',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DeriveSecurityLabel: Coding = {
  code: 'DRIVLABEL',
  display: 'derive security label',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DrugTherapy: Coding = {
  code: 'DRUG',
  display: 'Drug therapy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DrugInvoiceGroup: Coding = {
  code: 'DRUGING',
  display: 'drug invoice group',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DrugPolicy: Coding = {
  code: 'DRUGPOL',
  display: 'drug policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DrugProgram: Coding = {
  code: 'DRUGPRG',
  display: 'drug program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Discount: Coding = {
  code: 'DSC',
  display: 'discount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DuplicateTherapeuticAlassAlert: Coding = {
  code: 'DUPTHPCLS',
  display: 'duplicate therapeutic alass alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DuplicateGenericAlert: Coding = {
  code: 'DUPTHPGEN',
  display: 'duplicate generic alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DuplicateTherapyAlert: Coding = {
  code: 'DUPTHPY',
  display: 'Duplicate Therapy Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DevelopmentalDisabilityInformationSensitivity: Coding = {
  code: 'DVD',
  display: 'developmental disability information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ObservationDiagnosisTypes: Coding = {
  code: 'DX',
  display: 'ObservationDiagnosisTypes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EnvironmentalAllergy: Coding = {
  code: 'EALG',
  display: 'Environmental Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EmployeeAssistanceProgram: Coding = {
  code: 'EAP',
  display: 'employee assistance program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EducationFees: Coding = {
  code: 'EDU',
  display: 'education fees',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ElectronicFormToFollow: Coding = {
  code: 'EFORM',
  display: 'electronic form to follow',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ExtendedHealthcare: Coding = {
  code: 'EHCPOL',
  display: 'extended healthcare',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EnvironmentalIntolerance: Coding = {
  code: 'EINT',
  display: 'Environmental Intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Eligible: Coding = {
  code: 'ELG',
  display: 'Eligible',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Ellipse: Coding = {
  code: 'ELLIPSE',
  display: 'ellipse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EmergencySupply: Coding = {
  code: 'EM',
  display: 'Emergency Supply',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Emergency: Coding = {
  code: 'EMER',
  display: 'emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EmotionalDisturbanceInformationSensitivity: Coding = {
  code: 'EMOTDIS',
  display: 'emotional disturbance information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EmployeeInformationSensitivity: Coding = {
  code: 'EMP',
  display: 'employee information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EmployerInformationSensitivity: Coding = {
  code: 'EMPL',
  display: 'employer information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EmergencyOnly: Coding = {
  code: 'EMRGONLY',
  display: 'emergency only',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EnvironmentalNonAllergyIntolerance: Coding = {
  code: 'ENAINT',
  display: 'Environmental Non-Allergy Intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Encrypt: Coding = {
  code: 'ENCRYPT',
  display: 'encrypt',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EncryptAtRest: Coding = {
  code: 'ENCRYPTR',
  display: 'encrypt at rest',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EncryptInTransit: Coding = {
  code: 'ENCRYPTT',
  display: 'encrypt in transit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EncryptInUse: Coding = {
  code: 'ENCRYPTU',
  display: 'encrypt in use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EndogenousContent: Coding = {
  code: 'ENDC',
  display: 'endogenous content',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EndRenalProgram: Coding = {
  code: 'ENDRENAL',
  display: 'end renal program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EnvironmentalExposureTransmission: Coding = {
  code: 'ENVTRNS',
  display: 'environmental exposure transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EarlyPaymentFee: Coding = {
  code: 'EPYMT',
  display: 'early payment fee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ExtraordinaryServiceAssessment: Coding = {
  code: 'ESA',
  display: 'extraordinary service assessment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubstanceAbuseInformationSensitivity: Coding = {
  code: 'ETH',
  display: 'substance abuse information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AlcoholUseDisorderInformationSensitivity: Coding = {
  code: 'ETHUD',
  display: 'alcohol use disorder information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActSpecObsEvntfctsCode: Coding = {
  code: 'EVNFCTS',
  display: 'ActSpecObsEvntfctsCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_EmployeeWelfareBenefitPlanPolicy: Coding = {
  code: 'EWB',
  display: 'employee welfare benefit plan policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Final: Coding = {
  code: 'F',
  display: 'final',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FoodAllergy: Coding = {
  code: 'FALG',
  display: 'Food Allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FallsRiskAssessmentInstrumentTask: Coding = {
  code: 'FALLRISK',
  display: 'falls risk assessment instrument task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Fasting: Coding = {
  code: 'FAST',
  display: 'fasting',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FaxToFollow: Coding = {
  code: 'FAX',
  display: 'fax to follow',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Food: Coding = {
  code: 'FD',
  display: 'food',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Coating: Coding = {
  code: 'FDACOATING',
  display: 'coating',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Color: Coding = {
  code: 'FDACOLOR',
  display: 'color',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ImprintCode: Coding = {
  code: 'FDAIMPRINTCD',
  display: 'imprint code',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Logo: Coding = {
  code: 'FDALOGO',
  display: 'logo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Scoring: Coding = {
  code: 'FDASCORING',
  display: 'scoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Shape: Coding = {
  code: 'FDASHAPE',
  display: 'shape',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Size: Coding = {
  code: 'FDASIZE',
  display: 'size',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FecalOralTransmission: Coding = {
  code: 'FECTRNS',
  display: 'fecal-oral transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FirstFill: Coding = {
  code: 'FF',
  display: 'First Fill',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FirstFillComplete: Coding = {
  code: 'FFC',
  display: 'First Fill - Complete',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FirstFillCompletePartialStrength: Coding = {
  code: 'FFCS',
  display: 'first fill complete, partial strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FirstFillPartFill: Coding = {
  code: 'FFP',
  display: 'First Fill - Part Fill',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FirstFillPartFillPartialStrength: Coding = {
  code: 'FFPS',
  display: 'first fill, part fill, partial strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FeeForService: Coding = {
  code: 'FFS',
  display: 'fee for service',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FirstFillPartialStrength: Coding = {
  code: 'FFSS',
  display: 'first fill, partial strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FeeForServiceTopOff: Coding = {
  code: 'FFSTOP',
  display: 'fee for service top off',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Fibrin: Coding = {
  code: 'FIBRIN',
  display: 'Fibrin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Filtration: Coding = {
  code: 'FILT',
  display: 'Filtration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FinalizedDateTime: Coding = {
  code: 'FINALDT',
  display: 'finalized date/time',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Financial: Coding = {
  code: 'FINBILL',
  display: 'financial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FinancialInvoice: Coding = {
  code: 'FININV',
  display: 'financial invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FoodIntolerance: Coding = {
  code: 'FINT',
  display: 'Food Intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Field: Coding = {
  code: 'FLD',
  display: 'field',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FlexibleBenefitPlanPolicy: Coding = {
  code: 'FLEXP',
  display: 'flexible benefit plan policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FinancialManagementCompartment: Coding = {
  code: 'FMCOMPT',
  display: 'financial management compartment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FoodNonAllergyIntolerance: Coding = {
  code: 'FNAINT',
  display: 'Food Non-Allergy Intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FinalFee: Coding = {
  code: 'FNLFEE',
  display: 'final fee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FomiteTransmission: Coding = {
  code: 'FOMTRNS',
  display: 'fomite transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FoodBorneTransmission: Coding = {
  code: 'FOODTRNS',
  display: 'food-borne transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PrintOnForm: Coding = {
  code: 'FORM',
  display: 'Print on Form',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InvalidFormat: Coding = {
  code: 'FORMAT',
  display: 'invalid format',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FormulaDiet: Coding = {
  code: 'FORMULA',
  display: 'formula diet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FrameInvoiceGroup: Coding = {
  code: 'FRAMEING',
  display: 'frame invoice group',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PotentialFraud: Coding = {
  code: 'FRAUD',
  display: 'potential fraud',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FirstFee: Coding = {
  code: 'FRSTFEE',
  display: 'first fee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FloorStock: Coding = {
  code: 'FS',
  display: 'Floor stock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FederalSalesTax: Coding = {
  code: 'FST',
  display: 'federal sales tax',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Garnishee: Coding = {
  code: 'GARN',
  display: 'garnishee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GeneticDiseaseInformationSensitivity: Coding = {
  code: 'GDIS',
  display: 'genetic disease information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GeriatricAlert: Coding = {
  code: 'GEALRT',
  display: 'geriatric alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GeneticAlert: Coding = {
  code: 'GEN',
  display: 'Genetic Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GenderAlert: Coding = {
  code: 'GEND',
  display: 'Gender Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GenderAndSexualOrientationInformationSensitivity: Coding = {
  code: 'GENDER',
  display: 'gender and sexual orientation information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Gene: Coding = {
  code: 'GENE',
  display: 'gene',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_General: Coding = {
  code: 'GENRL',
  display: 'General',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GlutenFree: Coding = {
  code: 'GF',
  display: 'gluten free',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GoodFaithIndicator: Coding = {
  code: 'GFTH',
  display: 'good faith indicator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GISTier: Coding = {
  code: 'GISTIER',
  display: 'GIS tier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GoalList: Coding = {
  code: 'GOALLIST',
  display: 'goal list',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GovernmentEmployeeHealthProgram: Coding = {
  code: 'GOVEMP',
  display: 'government employee health program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Grade: Coding = {
  code: 'GRADE',
  display: 'grade',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GrantorChoice: Coding = {
  code: 'GRANTORCHOICE',
  display: 'grantor choice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_GlobalTradeItemNumber: Coding = {
  code: 'GTIN',
  display: 'Global Trade Item Number',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Guidance: Coding = {
  code: 'GUIDE',
  display: 'guidance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HCPCSLevelIIAndCarrierAssigned: Coding = {
  code: 'HCPCSA',
  display: 'HCPCS Level II and Carrier-assigned',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HealthRecord: Coding = {
  code: 'HEALTHREC',
  display: 'health record',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Hemolysis: Coding = {
  code: 'HEMOLYSIS',
  display: 'Hemolysis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HGHT: Coding = {
  code: 'HGHT',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HomeHealth: Coding = {
  code: 'HH',
  display: 'home health',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HouseholdSituationObservation: Coding = {
  code: 'HHOBS',
  display: 'household situation observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HealthInsurancePlanPolicy: Coding = {
  code: 'HIP',
  display: 'health insurance plan policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HIPAANoticeOfPrivacyPractices: Coding = {
  code: 'HIPAANOPP',
  display: 'HIPAA notice of privacy practices',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HIPAAPsychotherapyNotes: Coding = {
  code: 'HIPAAPsyNotes',
  display: 'HIPAA psychotherapy notes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HIPAASelfPay: Coding = {
  code: 'HIPAASelfPay',
  display: 'HIPAA self-pay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HighRiskPoolProgram: Coding = {
  code: 'HIRISK',
  display: 'high risk pool program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MedicationHistory: Coding = {
  code: 'HISTMEDLIST',
  display: 'medication history',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HIVAIDSInformationSensitivity: Coding = {
  code: 'HIV',
  display: 'HIV/AIDS information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HIVAIDSProgram: Coding = {
  code: 'HIVAIDS',
  display: 'HIV-AIDS program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HealthCareInteractionNotPatientCare: Coding = {
  code: 'HLTHCARE',
  display: 'Health Care Interaction - Not Patient Care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HealthMaintenanceOrganizationPolicy: Coding = {
  code: 'HMO',
  display: 'health maintenance organization policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CareGiverInteraction: Coding = {
  code: 'HOMECARE',
  display: 'Care Giver Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HospitalPatientInteraction: Coding = {
  code: 'HOSPPTNT',
  display: 'Hospital Patient Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HospitalVisitorInteraction: Coding = {
  code: 'HOSPVSTR',
  display: 'Hospital Visitor Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HouseholdInteraction: Coding = {
  code: 'HOUSEHLD',
  display: 'Household Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HumanResourceCompartment: Coding = {
  code: 'HRCOMPT',
  display: 'human resource compartment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HealthSpendingAccount: Coding = {
  code: 'HSAPOL',
  display: 'health spending account',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HarmonizedSalesTax: Coding = {
  code: 'HST',
  display: 'harmonized sales Tax',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HumanApproval: Coding = {
  code: 'HUAPRV',
  display: 'human approval',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HumanToHumanTransmission: Coding = {
  code: 'HUMHUMTRNS',
  display: 'human to human transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Isolation: Coding = {
  code: 'I',
  display: 'Isolation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InformationCollection: Coding = {
  code: 'ICOL',
  display: 'information collection',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Icterus: Coding = {
  code: 'ICTERUS',
  display: 'Icterus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Identified: Coding = {
  code: 'ID',
  display: 'Identified',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InformationDisclosure: Coding = {
  code: 'IDSCL',
  display: 'information disclosure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ImprovementNotation: Coding = {
  code: 'IDUR',
  display: 'improvement notation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Illegal: Coding = {
  code: 'ILLEGAL',
  display: 'illegal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ImageAttachment: Coding = {
  code: 'IMG',
  display: 'image attachment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ImmunizationListEntry: Coding = {
  code: 'IMMLE',
  display: 'immunization list entry',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ImmunizationListReview: Coding = {
  code: 'IMMLREV',
  display: 'immunization list review',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ImmunizationCategory: Coding = {
  code: 'IMMUCAT',
  display: 'immunization category',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Immunization: Coding = {
  code: 'IMMUNIZ',
  display: 'Immunization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InpatientEncounter: Coding = {
  code: 'IMP',
  display: 'inpatient encounter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ImpliedConsent: Coding = {
  code: 'IMPLIED',
  display: 'implied consent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ImpliedConsentWithOpportunityToDissent: Coding = {
  code: 'IMPLIEDD',
  display: 'implied consent with opportunity to dissent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_IndigenousPeoplesHealthProgram: Coding = {
  code: 'IND',
  display: 'indigenous peoples health program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ImagingStudyRequiringContrast: Coding = {
  code: 'IND01',
  display: 'imaging study requiring contrast',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ColonoscopyPrep: Coding = {
  code: 'IND02',
  display: 'colonoscopy prep',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Prophylaxis: Coding = {
  code: 'IND03',
  display: 'prophylaxis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SurgicalProphylaxis: Coding = {
  code: 'IND04',
  display: 'surgical prophylaxis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PregnancyProphylaxis: Coding = {
  code: 'IND05',
  display: 'pregnancy prophylaxis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_IndeterminateDiseaseTransmissionMode: Coding = {
  code: 'INDTRNS',
  display: 'indeterminate disease transmission mode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InformationAccess: Coding = {
  code: 'INFA',
  display: 'information access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccessOnly: Coding = {
  code: 'INFAO',
  display: 'access only',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AccessAndSaveOnly: Coding = {
  code: 'INFASO',
  display: 'access and save only',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AuthorizedInformationTransfer: Coding = {
  code: 'INFAUT',
  display: 'authorized information transfer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AfterExplicitConsent: Coding = {
  code: 'INFCON',
  display: 'after explicit consent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OnlyOnCourtOrder: Coding = {
  code: 'INFCRT',
  display: 'only on court order',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OnlyIfDangerToOthers: Coding = {
  code: 'INFDNG',
  display: 'only if danger to others',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OnlyInAnEmergency: Coding = {
  code: 'INFEMER',
  display: 'only in an emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OnlyIfPublicWelfareRisk: Coding = {
  code: 'INFPWR',
  display: 'only if public welfare risk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RegulatoryInformationTransfer: Coding = {
  code: 'INFREG',
  display: 'regulatory information transfer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InitialVolume: Coding = {
  code: 'INITIAL',
  display: 'Initial Volume',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InitialImmunization: Coding = {
  code: 'INITIMMUNIZ',
  display: 'Initial Immunization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InmateInteraction: Coding = {
  code: 'INMATE',
  display: 'Inmate Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_IntoleranceAlert: Coding = {
  code: 'INT',
  display: 'Intolerance Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_IntermediateDiagnosis: Coding = {
  code: 'INTDX',
  display: 'intermediate diagnosis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActSpecObsInterferenceCode: Coding = {
  code: 'INTFR',
  display: 'ActSpecObsInterferenceCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_IntimateInteraction: Coding = {
  code: 'INTIMATE',
  display: 'Intimate Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_IntoleranceList: Coding = {
  code: 'INTOLIST',
  display: 'intolerance list',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Investigational: Coding = {
  code: 'INV',
  display: 'investigational',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubmittedInvoice: Coding = {
  code: 'INVOICE',
  display: 'submitted invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InvoiceType: Coding = {
  code: 'INVTYPE',
  display: 'invoice type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InPosition: Coding = {
  code: 'IP',
  display: 'In Position',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InitialPopulation: Coding = {
  code: 'IPOP',
  display: 'initial population',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InitialPatientPopulation: Coding = {
  code: 'IPPOP',
  display: 'initial patient population',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InformationRedisclosure: Coding = {
  code: 'IRDSCL',
  display: 'information redisclosure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_IsolationAllowance: Coding = {
  code: 'ISOL',
  display: 'isolation allowance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DetectedIssue: Coding = {
  code: 'ISSUE',
  display: 'detected issue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ItemsCounted: Coding = {
  code: 'ITMCNT',
  display: 'items counted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Keyword: Coding = {
  code: 'KEY',
  display: 'keyword',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_UnknownKeyIdentifier: Coding = {
  code: 'KEY204',
  display: 'Unknown key identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DuplicateKeyIdentifier: Coding = {
  code: 'KEY205',
  display: 'Duplicate key identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NonMatchingIdentification: Coding = {
  code: 'KEY206',
  display: 'non-matching identification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_KnowledgeSubject: Coding = {
  code: 'KSUBJ',
  display: 'knowledge subject',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_KnowledgeSubtopic: Coding = {
  code: 'KSUBT',
  display: 'knowledge subtopic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LeftEquipment: Coding = {
  code: 'L',
  display: 'Left Equipment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LabAlert: Coding = {
  code: 'LAB',
  display: 'Lab Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LabTestCategory: Coding = {
  code: 'LABCAT',
  display: 'lab test category',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LaboratoryTestOrderEntryTask: Coding = {
  code: 'LABOE',
  display: 'laboratory test order entry task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LabResults: Coding = {
  code: 'LABRESULTS',
  display: 'lab results',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LaboratoryResultsReviewTask: Coding = {
  code: 'LABRREV',
  display: 'laboratory results review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LactationAlert: Coding = {
  code: 'LACT',
  display: 'Lactation Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LactationTransmission: Coding = {
  code: 'LACTTRNS',
  display: 'lactation transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LateInvoice: Coding = {
  code: 'LATE',
  display: 'late invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LawEnforcementTransport: Coding = {
  code: 'LAWENF',
  display: 'law enforcement transport',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LDLPrecipitation: Coding = {
  code: 'LDLP',
  display: 'LDL Precipitation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LengthIsTooLong: Coding = {
  code: 'LEN_LONG',
  display: 'length is too long',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LengthOutOfRange: Coding = {
  code: 'LEN_RANGE',
  display: 'length out of range',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LengthIsTooShort: Coding = {
  code: 'LEN_SHORT',
  display: 'length is too short',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LensInvoiceGroup: Coding = {
  code: 'LENSING',
  display: 'lens invoice group',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LowFat: Coding = {
  code: 'LF',
  display: 'low fat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LifeTimeMaximum: Coding = {
  code: 'LFEMX',
  display: 'life time maximum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LicensedGeneralPhysicianCare: Coding = {
  code: 'LGPC',
  display: 'licensed general physician care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LifeInsurancePolicy: Coding = {
  code: 'LIFE',
  display: 'life insurance policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Lipemia: Coding = {
  code: 'LIPEMIA',
  display: 'Lipemia',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LivingArrangementInformationSensitivity: Coding = {
  code: 'LIVARG',
  display: 'living arrangement information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Loan: Coding = {
  code: 'LOAN',
  display: 'Loan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Location: Coding = {
  code: 'LOC',
  display: 'location',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LocationInformationSensitivity: Coding = {
  code: 'LOCIS',
  display: 'location information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LowProtein: Coding = {
  code: 'LP',
  display: 'low protein',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Liquid: Coding = {
  code: 'LQ',
  display: 'liquid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LegitimateRelationshipCompartment: Coding = {
  code: 'LRCOMPT',
  display: 'legitimate relationship compartment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LowSodium: Coding = {
  code: 'LS',
  display: 'low sodium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LongTermCarePolicy: Coding = {
  code: 'LTC',
  display: 'long term care policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LongTermCareFacilityInteraction: Coding = {
  code: 'LTRMCARE',
  display: 'Long Term Care Facility Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LimitedUse: Coding = {
  code: 'LU',
  display: 'limited use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Missing: Coding = {
  code: 'M',
  display: 'Missing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MandatoryHealthProgram: Coding = {
  code: 'MANDPOL',
  display: 'mandatory health program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ManualReview: Coding = {
  code: 'MANUAL',
  display: 'manual review',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MarkupOrUpCharge: Coding = {
  code: 'MARKUP',
  display: 'markup or up-charge',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MaritalStatusInformationSensitivity: Coding = {
  code: 'MARST',
  display: 'marital status information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MedicationAdministrationRecordWorkListReviewTask: Coding = {
  code: 'MARWLREV',
  display: 'medication administration record work list review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RepetitionsAboveMaximum: Coding = {
  code: 'MAXOCCURS',
  display: 'repetitions above maximum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ManagedCarePolicy: Coding = {
  code: 'MCPOL',
  display: 'managed care policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MaximumDosageReached: Coding = {
  code: 'MDOSE',
  display: 'maximum dosage reached',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Medical: Coding = {
  code: 'MED',
  display: 'Medical',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MedicalConditionCategory: Coding = {
  code: 'MEDCCAT',
  display: 'medical condition category',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MedicationList: Coding = {
  code: 'MEDLIST',
  display: 'medication list',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MedicationOrderEntryTask: Coding = {
  code: 'MEDOE',
  display: 'medication order entry task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MeasurementEndDate: Coding = {
  code: 'MEDT',
  display: 'measurement end date',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MentalHealthCategory: Coding = {
  code: 'MENCAT',
  display: 'mental health category',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MentalHealthPolicy: Coding = {
  code: 'MENTPOL',
  display: 'mental health policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MentalHealthProgram: Coding = {
  code: 'MENTPRG',
  display: 'mental health program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MentalHealthInformationSensitivity: Coding = {
  code: 'MH',
  display: 'mental health information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MicrobiologyOrganismsResultsReviewTask: Coding = {
  code: 'MICROORGRREV',
  display: 'microbiology organisms results review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MicrobiologyResultsReviewTask: Coding = {
  code: 'MICRORREV',
  display: 'microbiology results review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MicrobiologySensitivityTestResultsReviewTask: Coding = {
  code: 'MICROSENSRREV',
  display: 'microbiology sensitivity test results review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MilitaryHealthProgram: Coding = {
  code: 'MILITARY',
  display: 'military health program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MinimumNecessary: Coding = {
  code: 'MINEC',
  display: 'minimum necessary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RepetitionsBelowMinimum: Coding = {
  code: 'MINOCCURS',
  display: 'repetitions below minimum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MissedAppointment: Coding = {
  code: 'MISSAPT',
  display: 'missed appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ConditionalElementMissing: Coding = {
  code: 'MISSCOND',
  display: 'conditional element missing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MandatoryElementMissing: Coding = {
  code: 'MISSMAND',
  display: 'mandatory element missing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MedicationListReviewTask: Coding = {
  code: 'MLREV',
  display: 'medication list review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Model: Coding = {
  code: 'MODEL',
  display: 'model',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Month: Coding = {
  code: 'MONTH',
  display: 'month',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ManufacturerSample: Coding = {
  code: 'MS',
  display: 'Manufacturer Sample',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MeasurementStartDate: Coding = {
  code: 'MSD',
  display: 'measurement start date',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RiskAdjustment: Coding = {
  code: 'MSRADJ',
  display: 'risk adjustment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RateAggregation: Coding = {
  code: 'MSRAGG',
  display: 'rate aggregation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HealthQualityMeasureImprovementNotation: Coding = {
  code: 'MSRIMPROV',
  display: 'health quality measure improvement notation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Jurisdiction: Coding = {
  code: 'MSRJUR',
  display: 'jurisdiction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MeasureObservation: Coding = {
  code: 'MSROBS',
  display: 'measure observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MeasurePopulation: Coding = {
  code: 'MSRPOPL',
  display: 'measure population',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MeasurePopulationExclusions: Coding = {
  code: 'MSRPOPLEX',
  display: 'measure population exclusions',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ReporterType: Coding = {
  code: 'MSRRPTR',
  display: 'reporter type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TimeframeForReporting: Coding = {
  code: 'MSRRPTTIME',
  display: 'timeframe for reporting',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MeasureScoring: Coding = {
  code: 'MSRSCORE',
  display: 'measure scoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HealthQualityMeasureCareSetting: Coding = {
  code: 'MSRSET',
  display: 'health quality measure care setting',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HealthQualityMeasureTopicType: Coding = {
  code: 'MSRTOPIC',
  display: 'health quality measure topic type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MeasurementPeriod: Coding = {
  code: 'MSRTP',
  display: 'measurement period',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MeasureType: Coding = {
  code: 'MSRTYPE',
  display: 'measure type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MilitarySexualTraumaInformationSensitivity: Coding = {
  code: 'MST',
  display: 'military sexual trauma information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MotorVehicleAccident: Coding = {
  code: 'MVA',
  display: 'Motor vehicle accident',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NormalDiet: Coding = {
  code: 'N',
  display: 'normal diet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NonAllergyIntolerance: Coding = {
  code: 'NAINT',
  display: 'Non-Allergy Intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InsufficientAuthorization: Coding = {
  code: 'NAT',
  display: 'Insufficient authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NotAuthorized: Coding = {
  code: 'NAUTH',
  display: 'Not Authorized',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NotEligible: Coding = {
  code: 'NELG',
  display: 'Not Eligible',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NetAmount: Coding = {
  code: 'NETAMT',
  display: 'Net Amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Neutralization: Coding = {
  code: 'NEUT',
  display: 'Neutralization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoFat: Coding = {
  code: 'NF',
  display: 'no fat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoDisclosureWithoutSubjectAuthorization: Coding = {
  code: 'NOAUTH',
  display: 'no disclosure without subject authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoCollection: Coding = {
  code: 'NOCOLLECT',
  display: 'no collection',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoConsent: Coding = {
  code: 'NOCONSENT',
  display: 'no consent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoDisclosureWithoutConsentDirective: Coding = {
  code: 'NODSCLCD',
  display: 'no disclosure without consent directive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoDisclosureWithoutInformationSubjectQuoteSConsentDirective: Coding = {
  code: 'NODSCLCDS',
  display: "no disclosure without information subject's consent directive",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DuplicateValuesAreNotPermitted: Coding = {
  code: 'NODUPS',
  display: 'duplicate values are not permitted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NatureOfInjury: Coding = {
  code: 'NOI',
  display: 'nature of injury',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoIntegration: Coding = {
  code: 'NOINTEGRATE',
  display: 'no integration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoUnlistedEntityDisclosure: Coding = {
  code: 'NOLIST',
  display: 'no unlisted entity disclosure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoDisclosureWithoutMOU: Coding = {
  code: 'NOMOU',
  display: 'no disclosure without MOU',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NonPaymentData: Coding = {
  code: 'NON',
  display: 'Non-Payment Data',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InpatientNonAcute: Coding = {
  code: 'NONAC',
  display: 'inpatient non-acute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoDisclosureWithoutOrganizationalAuthorization: Coding = {
  code: 'NOORGPOL',
  display: 'no disclosure without organizational authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoDisclosureToPatientFamilyOrCaregiversWithoutAttendingProviderQuoteSAuthorization: Coding = {
  code: 'NOPAT',
  display: "no disclosure to patient, family or caregivers without attending provider's authorization",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ElementWillNotBePersisted: Coding = {
  code: 'NOPERSIST',
  display: 'element will not be persisted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoCollectionBeyondPurposeOfUse: Coding = {
  code: 'NOPERSISTP',
  display: 'no collection beyond purpose of use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoticeOfPrivacyPractices: Coding = {
  code: 'NOPP',
  display: 'notice of privacy practices',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoRedisclosureWithoutConsentDirective: Coding = {
  code: 'NORDSCLCD',
  display: 'no redisclosure without consent directive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoRedisclosureWithoutInformationSubjectQuoteSConsentDirective: Coding = {
  code: 'NORDSCLCDS',
  display: "no redisclosure without information subject's consent directive",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoDisclosureWithoutJurisdictionalAuthorization: Coding = {
  code: 'NORDSCLW',
  display: 'no disclosure without jurisdictional authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoRelinking: Coding = {
  code: 'NORELINK',
  display: 'no relinking',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoReuseBeyondPurposeOfUse: Coding = {
  code: 'NOREUSE',
  display: 'no reuse beyond purpose of use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NosocomialTransmission: Coding = {
  code: 'NOSTRNS',
  display: 'nosocomial transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoUnauthorizedVIPDisclosure: Coding = {
  code: 'NOVIP',
  display: 'no unauthorized VIP disclosure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Numerator: Coding = {
  code: 'NUMER',
  display: 'numerator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NumeratorExclusions: Coding = {
  code: 'NUMEX',
  display: 'numerator exclusions',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_InProcess: Coding = {
  code: 'O',
  display: 'In Process',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ObligationPolicy: Coding = {
  code: 'ObligationPolicy',
  display: 'obligation policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Obstetrics: Coding = {
  code: 'OBS',
  display: 'Obstetrics',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ObservationAlert: Coding = {
  code: 'OBSA',
  display: 'Observation Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AntigenCount: Coding = {
  code: 'OBSANTC',
  display: 'antigen count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AntigenValidity: Coding = {
  code: 'OBSANTV',
  display: 'antigen validity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ObservationEncounter: Coding = {
  code: 'OBSENC',
  display: 'observation encounter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ObsoleteRecordReturned: Coding = {
  code: 'OBSOLETE',
  display: 'obsolete record returned',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OrderEntryTask: Coding = {
  code: 'OE',
  display: 'order entry task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OralHealthService: Coding = {
  code: 'OHSINV',
  display: 'oral health service',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Intolerance: Coding = {
  code: 'OINT',
  display: 'intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Oncology: Coding = {
  code: 'ONC',
  display: 'Oncology',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OneTime: Coding = {
  code: 'ONET',
  display: 'one time',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OutOfJurisdiction: Coding = {
  code: 'OOJ',
  display: 'out of jurisdiction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OutOfOffice: Coding = {
  code: 'OOO',
  display: 'out of office',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OpioidUseDisorderInformationSensitivity: Coding = {
  code: 'OPIOIDUD',
  display: 'opioid use disorder information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OptIn: Coding = {
  code: 'OPTIN',
  display: 'opt-in',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OptInWithRestrictions: Coding = {
  code: 'OPTINR',
  display: 'opt-in with restrictions',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OpOut: Coding = {
  code: 'OPTOUT',
  display: 'op-out',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OptOutWithExceptions: Coding = {
  code: 'OPTOUTE',
  display: 'opt-out with exceptions',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoDisclosureWithoutOriginatorAuthorization: Coding = {
  code: 'ORCON',
  display: 'no disclosure without originator authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OrdersReviewTask: Coding = {
  code: 'OREV',
  display: 'orders review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_OrthodonticService: Coding = {
  code: 'ORTHO',
  display: 'orthodontic service',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NonPrescriptionMedicine: Coding = {
  code: 'OTC',
  display: 'non prescription medicine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Private: Coding = {
  code: 'P',
  display: 'Private',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PatientAdministrationCompartment: Coding = {
  code: 'PACOMPT',
  display: 'patient administration compartment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PhenylalanineFree: Coding = {
  code: 'PAF',
  display: 'phenylalanine free',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PreferredAccommodationInvoice: Coding = {
  code: 'PAINV',
  display: 'preferred accommodation invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Palliative: Coding = {
  code: 'PALL',
  display: 'Palliative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaperDocumentationToFollow: Coding = {
  code: 'PAPER',
  display: 'paper documentation to follow',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Parenteral: Coding = {
  code: 'PAR',
  display: 'parenteral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ParenteralTransmission: Coding = {
  code: 'PARTRNS',
  display: 'parenteral transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PatientAdverseEvent: Coding = {
  code: 'PAT_ADV_EVNT',
  display: 'patient adverse event',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PatientDocumentationTask: Coding = {
  code: 'PATDOC',
  display: 'patient documentation task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PatientEducationEntry: Coding = {
  code: 'PATEDUE',
  display: 'patient education entry',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PatientInformationReviewTask: Coding = {
  code: 'PATINFO',
  display: 'patient information review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PathologyReportEntryTask: Coding = {
  code: 'PATREPE',
  display: 'pathology report entry task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PathologyReportReviewTask: Coding = {
  code: 'PATREPREV',
  display: 'pathology report review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Payment: Coding = {
  code: 'PAY',
  display: 'payment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Payee: Coding = {
  code: 'PAYEE',
  display: 'payee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Payor: Coding = {
  code: 'PAYOR',
  display: 'payor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNullifiedPriorPeriodElectronicAmount: Coding = {
  code: 'PDNFPPELAT',
  display: 'paid nullified prior-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNullifiedPriorPeriodElectronicCount: Coding = {
  code: 'PDNFPPELCT',
  display: 'paid nullified prior-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNullifiedPriorPeriodManualAmount: Coding = {
  code: 'PDNFPPMNAT',
  display: 'paid nullified prior-period manual amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNullifiedPriorPeriodManualCount: Coding = {
  code: 'PDNFPPMNCT',
  display: 'paid nullified prior-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNullifiedSamePeriodElectronicAmount: Coding = {
  code: 'PDNFSPELAT',
  display: 'paid nullified same-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNullifiedSamePeriodElectronicCount: Coding = {
  code: 'PDNFSPELCT',
  display: 'paid nullified same-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNullifiedSamePeriodManualAmount: Coding = {
  code: 'PDNFSPMNAT',
  display: 'paid nullified same-period manual amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNullifiedSamePeriodManualCount: Coding = {
  code: 'PDNFSPMNCT',
  display: 'paid nullified same-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNonPayeePayablePriorPeriodElectronicAmount: Coding = {
  code: 'PDNPPPELAT',
  display: 'paid non-payee payable prior-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNonPayeePayablePriorPeriodElectronicCount: Coding = {
  code: 'PDNPPPELCT',
  display: 'paid non-payee payable prior-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNonPayeePayablePriorPeriodManualAmount: Coding = {
  code: 'PDNPPPMNAT',
  display: 'paid non-payee payable prior-period manual amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNonPayeePayablePriorPeriodManualCount: Coding = {
  code: 'PDNPPPMNCT',
  display: 'paid non-payee payable prior-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNonPayeePayableSamePeriodElectronicAmount: Coding = {
  code: 'PDNPSPELAT',
  display: 'paid non-payee payable same-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNonPayeePayableSamePeriodElectronicCount: Coding = {
  code: 'PDNPSPELCT',
  display: 'paid non-payee payable same-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNonPayeePayableSamePeriodManualAmount: Coding = {
  code: 'PDNPSPMNAT',
  display: 'paid non-payee payable same-period manual amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidNonPayeePayableSamePeriodManualCount: Coding = {
  code: 'PDNPSPMNCT',
  display: 'paid non-payee payable same-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidPayeePayablePriorPeriodElectronicAmount: Coding = {
  code: 'PDPPPPELAT',
  display: 'paid payee payable prior-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidPayeePayablePriorPeriodElectronicCount: Coding = {
  code: 'PDPPPPELCT',
  display: 'paid payee payable prior-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidPayeePayablePriorPeriodManualAmount: Coding = {
  code: 'PDPPPPMNAT',
  display: 'paid payee payable prior-period manual amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidPayeePayablePriorPeriodManualCount: Coding = {
  code: 'PDPPPPMNCT',
  display: 'paid payee payable prior-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidPayeePayableSamePeriodElectronicAmount: Coding = {
  code: 'PDPPSPELAT',
  display: 'paid payee payable same-period electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidPayeePayableSamePeriodElectronicCount: Coding = {
  code: 'PDPPSPELCT',
  display: 'paid payee payable same-period electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidPayeePayableSamePeriodManualAmount: Coding = {
  code: 'PDPPSPMNAT',
  display: 'paid payee payable same-period manual amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaidPayeePayableSamePeriodManualCount: Coding = {
  code: 'PDPPSPMNCT',
  display: 'paid payee payable same-period manual count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PatientDefaultInformationSensitivity: Coding = {
  code: 'PDS',
  display: 'patient default information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PediatricAlert: Coding = {
  code: 'PEALRT',
  display: 'pediatric alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Pediatrics: Coding = {
  code: 'PED',
  display: 'Pediatrics',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PeriodicFee: Coding = {
  code: 'PERFEE',
  display: 'periodic fee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Period: Coding = {
  code: 'PERIOD',
  display: 'period',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PerformanceBonus: Coding = {
  code: 'PERMBNS',
  display: 'performance bonus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PersistSecurityLabel: Coding = {
  code: 'PERSISTLABEL',
  display: 'persist security label',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Pharmaceutical: Coding = {
  code: 'PHAR',
  display: 'Pharmaceutical',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PhysicianRequestedInformationSensitivity: Coding = {
  code: 'PHY',
  display: 'physician requested information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PhysicalRehab: Coding = {
  code: 'PHYRHB',
  display: 'Physical Rehab',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PublicInsuranceExhausted: Coding = {
  code: 'PIE',
  display: 'public insurance exhausted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PaperInvoice: Coding = {
  code: 'PINV',
  display: 'paper invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CommonSpaceInteraction: Coding = {
  code: 'PLACE',
  display: 'Common Space Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TransplacentalTransmission: Coding = {
  code: 'PLACTRNS',
  display: 'transplacental transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PolyOrdererAlert: Coding = {
  code: 'PLYDOC',
  display: 'Poly-orderer Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PolySupplierAlert: Coding = {
  code: 'PLYPHRM',
  display: 'Poly-supplier Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PropertyAndCasualtyInsurancePolicy: Coding = {
  code: 'PNC',
  display: 'property and casualty insurance policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Point: Coding = {
  code: 'POINT',
  display: 'point',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Polyline: Coding = {
  code: 'POLY',
  display: 'polyline',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PointOfServicePolicy: Coding = {
  code: 'POS',
  display: 'point of service policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PreferredProviderOrganizationPolicy: Coding = {
  code: 'PPO',
  display: 'preferred provider organization policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PriorPeriodAdjustment: Coding = {
  code: 'PPRD',
  display: 'prior period adjustment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PRA: Coding = {
  code: 'PRA',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ProductInvoiceGroup: Coding = {
  code: 'PRDING',
  display: 'product invoice group',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PeriodMaximum: Coding = {
  code: 'PRDMX',
  display: 'period maximum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PreDilution: Coding = {
  code: 'PRE',
  display: 'Pre-Dilution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PreferenceStrength: Coding = {
  code: 'PREFSTRENGTH',
  display: 'preference strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PregnancyAlert: Coding = {
  code: 'PREG',
  display: 'Pregnancy Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PreAdmission: Coding = {
  code: 'PRENC',
  display: 'pre-admission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PrivacyMark: Coding = {
  code: 'PRIVMARK',
  display: 'privacy mark',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Preliminary: Coding = {
  code: 'PRLMN',
  display: 'preliminary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AsNeeded: Coding = {
  code: 'PRN',
  display: 'as needed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ProfessionalAssociationDeduction: Coding = {
  code: 'PROA',
  display: 'professional association deduction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ProblemList: Coding = {
  code: 'PROBLIST',
  display: 'problem list',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ProblemListEntryTask: Coding = {
  code: 'PROBLISTE',
  display: 'problem list entry task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ProblemListReviewTask: Coding = {
  code: 'PROBLISTREV',
  display: 'problem list review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Provider: Coding = {
  code: 'PROV',
  display: 'provider',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PatientRequestedInformationSensitivity: Coding = {
  code: 'PRS',
  display: 'patient requested information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PrivateTransport: Coding = {
  code: 'PRVTRN',
  display: 'private transport',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ProvincialStateSalesTax: Coding = {
  code: 'PST',
  display: 'provincial/state sales tax',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ProfessionalServiceCategory: Coding = {
  code: 'PSVCCAT',
  display: 'professional service category',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PsychiatryDisorderInformationSensitivity: Coding = {
  code: 'PSY',
  display: 'psychiatry disorder information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Psychiatric: Coding = {
  code: 'PSYCH',
  display: 'Psychiatric',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PsychotherapyNoteInformationSensitivity: Coding = {
  code: 'PSYTHPN',
  display: 'psychotherapy note information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_HealthCareInteractionPatientCare: Coding = {
  code: 'PTNTCARE',
  display: 'Health Care Interaction - Patient Care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PublicHealthcare: Coding = {
  code: 'PUBLICPOL',
  display: 'public healthcare',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PublicTransport: Coding = {
  code: 'PUBTRN',
  display: 'public transport',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DelayedByAPreviousPayor: Coding = {
  code: 'PYRDELAY',
  display: 'delayed by a previous payor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ProcessCompleted: Coding = {
  code: 'R',
  display: 'Process Completed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RaceInformationSensitivity: Coding = {
  code: 'RACE',
  display: 'race information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RadiologyReportEntryTask: Coding = {
  code: 'RADREPE',
  display: 'radiology report entry task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RadiologyReportReviewTask: Coding = {
  code: 'RADREPREV',
  display: 'radiology report review task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RelatedAllergyAlert: Coding = {
  code: 'RALG',
  display: 'Related Allergy Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RelatedPriorReactionAlert: Coding = {
  code: 'RAR',
  display: 'Related Prior Reaction Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Rationale: Coding = {
  code: 'RAT',
  display: 'rationale',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ReductionDiet: Coding = {
  code: 'RD',
  display: 'reduction diet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ReactionAlert: Coding = {
  code: 'REACT',
  display: 'Reaction Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Recalcification: Coding = {
  code: 'RECA',
  display: 'Recalcification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Recovery: Coding = {
  code: 'RECOV',
  display: 'recovery',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Redact: Coding = {
  code: 'REDACT',
  display: 'redact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Reference: Coding = {
  code: 'REF',
  display: 'reference',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ReflexPermission: Coding = {
  code: 'REFLEX',
  display: 'reflex permission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ReferralNotRequired: Coding = {
  code: 'REFNR',
  display: 'referral not required',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RefrainPolicy: Coding = {
  code: 'RefrainPolicy',
  display: 'refrain policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ReinsurancePolicy: Coding = {
  code: 'REI',
  display: 'reinsurance policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ReligionInformationSensitivity: Coding = {
  code: 'REL',
  display: 'religion information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ReminderListEntry: Coding = {
  code: 'REMLE',
  display: 'reminder list entry',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ReminderListReview: Coding = {
  code: 'REMLREV',
  display: 'reminder list review',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Rent: Coding = {
  code: 'RENT',
  display: 'Rent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RepresentativeHalfLife: Coding = {
  code: 'REP_HALF_LIFE',
  display: 'representative half-life',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RepetitionsOutOfRange: Coding = {
  code: 'REP_RANGE',
  display: 'repetitions out of range',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ECGRepresentativeBeatWaveforms: Coding = {
  code: 'REPRESENTATIVE_BEAT',
  display: 'ECG representative beat waveforms',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RepeatedService: Coding = {
  code: 'REPSERV',
  display: 'repeated service',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RerunDilution: Coding = {
  code: 'RERUN',
  display: 'Rerun Dilution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ResearchProjectCompartment: Coding = {
  code: 'RESCOMPT',
  display: 'research project compartment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ResearchInformationAccess: Coding = {
  code: 'RESEARCH',
  display: 'research information access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RestockingFee: Coding = {
  code: 'RESTOCK',
  display: 'restocking fee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RetireeHealthProgram: Coding = {
  code: 'RETIRE',
  display: 'retiree health program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RetroAdjustment: Coding = {
  code: 'RETRO',
  display: 'retro adjustment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_StandardChargeReversal: Coding = {
  code: 'REV',
  display: 'Standard Charge Reversal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Refill: Coding = {
  code: 'RF',
  display: 'Refill',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RefillComplete: Coding = {
  code: 'RFC',
  display: 'Refill - Complete',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RefillCompletePartialStrength: Coding = {
  code: 'RFCS',
  display: 'refill complete partial strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RefillFirstFillThisFacility: Coding = {
  code: 'RFF',
  display: 'Refill (First fill this facility)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RefillPartialStrengthFirstFillThisFacility: Coding = {
  code: 'RFFS',
  display: 'refill partial strength (first fill this facility)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RefillPartFill: Coding = {
  code: 'RFP',
  display: 'Refill - Part Fill',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RefillPartFillPartialStrength: Coding = {
  code: 'RFPS',
  display: 'refill part fill partial strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RefillPartialStrength: Coding = {
  code: 'RFS',
  display: 'refill partial strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ECGRhythmWaveforms: Coding = {
  code: 'RHYTHM',
  display: 'ECG rhythm waveforms',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RelatedIntoleranceAlert: Coding = {
  code: 'RINT',
  display: 'Related Intolerance Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RiskAssessmentInstrumentTask: Coding = {
  code: 'RISKASSESS',
  display: 'risk assessment instrument task',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RiskFactors: Coding = {
  code: 'RISKLIST',
  display: 'risk factors',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RecordsManagementCompartment: Coding = {
  code: 'RMGTCOMPT',
  display: 'records management compartment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_FullySpecifiedROI: Coding = {
  code: 'ROIFS',
  display: 'fully specified ROI',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PartiallySpecifiedROI: Coding = {
  code: 'ROIPS',
  display: 'partially specified ROI',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RosterFunding: Coding = {
  code: 'ROST',
  display: 'roster funding',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RelatedReactionAlert: Coding = {
  code: 'RREACT',
  display: 'Related Reaction Alert',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_DeIdentifiedInformationAccess: Coding = {
  code: 'RSDID',
  display: 'de-identified information access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ReIdentifiableInformationAccess: Coding = {
  code: 'RSREID',
  display: 're-identifiable information access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_PrescriptionOnlyMedicine: Coding = {
  code: 'RX',
  display: 'prescription only medicine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_MedicationCategory: Coding = {
  code: 'RXCAT',
  display: 'medication category',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RxCompoundInvoice: Coding = {
  code: 'RXCINV',
  display: 'Rx compound invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RxDispenseInvoice: Coding = {
  code: 'RXDINV',
  display: 'Rx dispense invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Suite: Coding = {
  code: 'S',
  display: 'Suite',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SpecialAuthorization: Coding = {
  code: 'SA',
  display: 'special authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SpecialAccess: Coding = {
  code: 'SAC',
  display: 'special access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SafetyNetClinicProgram: Coding = {
  code: 'SAFNET',
  display: 'safety net clinic program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Sale: Coding = {
  code: 'SALE',
  display: 'Sale',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubmittedBilledElectronicAmount: Coding = {
  code: 'SBBLELAT',
  display: 'submitted billed electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubmittedBilledElectronicCount: Coding = {
  code: 'SBBLELCT',
  display: 'submitted billed electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SessionalOrBlockFeeInvoice: Coding = {
  code: 'SBFINV',
  display: 'sessional or block fee invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubmittedNullifiedElectronicAmount: Coding = {
  code: 'SBNFELAT',
  display: 'submitted nullified electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubmittedCancelledElectronicCount: Coding = {
  code: 'SBNFELCT',
  display: 'submitted cancelled electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubmittedPendingElectronicAmount: Coding = {
  code: 'SBPDELAT',
  display: 'submitted pending electronic amount',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubmittedPendingElectronicCount: Coding = {
  code: 'SBPDELCT',
  display: 'submitted pending electronic count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SickleCellAnemiaInformationSensitivity: Coding = {
  code: 'SCA',
  display: 'sickle cell anemia information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SchonkostGE: Coding = {
  code: 'SCH',
  display: 'schonkost (GE)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_School: Coding = {
  code: 'SCHL',
  display: 'school',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SchoolDivision: Coding = {
  code: 'SCHLDIV',
  display: 'school division',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SchoolAccident: Coding = {
  code: 'SCHOOL',
  display: 'School Accident',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SchoolInteraction: Coding = {
  code: 'SCHOOL2',
  display: 'School Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SupplementalDataElements: Coding = {
  code: 'SDE',
  display: 'supplemental data elements',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SexualAssaultAbuseOrDomesticViolenceInformationSensitivity: Coding = {
  code: 'SDV',
  display: 'sexual assault, abuse, or domestic violence information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SecurityAlterationIntegrityObservation: Coding = {
  code: 'SECALTINTOBS',
  display: 'security alteration integrity observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SecurityCategoryObservation: Coding = {
  code: 'SECCATOBS',
  display: 'security category observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SecurityClassificationObservation: Coding = {
  code: 'SECCLASSOBS',
  display: 'security classification observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SecurityControlObservation: Coding = {
  code: 'SECCONOBS',
  display: 'security control observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SecurityDataIntegrityObservation: Coding = {
  code: 'SECDATINTOBS',
  display: 'security data integrity observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SecurityIntegrityConfidenceObservation: Coding = {
  code: 'SECINTCONOBS',
  display: 'security integrity confidence observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SecurityIntegrityObservation: Coding = {
  code: 'SECINTOBS',
  display: 'security integrity observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SecurityIntegrityProvenanceAssertedByObservation: Coding = {
  code: 'SECINTPRVABOBS',
  display: 'security integrity provenance asserted by observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SecurityIntegrityProvenanceObservation: Coding = {
  code: 'SECINTPRVOBS',
  display: 'security integrity provenance observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SecurityIntegrityProvenanceReportedByObservation: Coding = {
  code: 'SECINTPRVRBOBS',
  display: 'security integrity provenance reported by observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SecurityIntegrityStatusObservation: Coding = {
  code: 'SECINTSTOBS',
  display: 'security integrity status observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SecurityObservationType: Coding = {
  code: 'SECOBS',
  display: 'SecurityObservationType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SECTRSTOBS: Coding = {
  code: 'SECTRSTOBS',
  display: 'SECTRSTOBS',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SecurityPolicy: Coding = {
  code: 'SecurityPolicy',
  display: 'security policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SendingApplication: Coding = {
  code: 'SENDAPP',
  display: 'sending application',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SessionalFunding: Coding = {
  code: 'SESS',
  display: 'sessional funding',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SeverityObservation: Coding = {
  code: 'SEV',
  display: 'Severity Observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SexualityAndReproductiveHealthInformationSensitivity: Coding = {
  code: 'SEX',
  display: 'sexuality and reproductive health information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SexualTransmission: Coding = {
  code: 'SEXTRNS',
  display: 'sexual transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SickleCell: Coding = {
  code: 'SICKLE',
  display: 'sickle cell',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ScriptOwing: Coding = {
  code: 'SO',
  display: 'Script Owing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SocialServiceProgram: Coding = {
  code: 'SOCIAL',
  display: 'social service program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SocialExtendedFamilyInteraction: Coding = {
  code: 'SOCIAL2',
  display: 'Social/Extended Family Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SemiPrivate: Coding = {
  code: 'SP',
  display: 'Semi-private',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SpendDown: Coding = {
  code: 'SPEND',
  display: 'spend down',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SpeciallyProtectedInformationSensitivity: Coding = {
  code: 'SPI',
  display: 'specially protected information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Coating_2: Coding = {
  code: 'SPLCOATING',
  display: 'coating',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Color_2: Coding = {
  code: 'SPLCOLOR',
  display: 'color',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Image: Coding = {
  code: 'SPLIMAGE',
  display: 'image',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Imprint: Coding = {
  code: 'SPLIMPRINT',
  display: 'imprint',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Scoring_2: Coding = {
  code: 'SPLSCORING',
  display: 'scoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Shape_2: Coding = {
  code: 'SPLSHAPE',
  display: 'shape',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Size_2: Coding = {
  code: 'SPLSIZE',
  display: 'size',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Symbol: Coding = {
  code: 'SPLSYMBOL',
  display: 'symbol',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SportingAccident: Coding = {
  code: 'SPT',
  display: 'Sporting Accident',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SpecimenReceived: Coding = {
  code: 'SREC',
  display: 'specimen received',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ShortStay: Coding = {
  code: 'SS',
  display: 'short stay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SensitiveServiceProviderInformationSensitivity: Coding = {
  code: 'SSP',
  display: 'sensitive service provider information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SpecimenInStorage: Coding = {
  code: 'SSTOR',
  display: 'specimen in storage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SexuallyTransmittedDiseaseInformationSensitivity: Coding = {
  code: 'STD',
  display: 'sexually transmitted disease information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Storage: Coding = {
  code: 'STORE',
  display: 'Storage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SpecimenInTransit: Coding = {
  code: 'STRAN',
  display: 'specimen in transit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Stratification: Coding = {
  code: 'STRAT',
  display: 'stratification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubstanceUsePolicy: Coding = {
  code: 'SUBPOL',
  display: 'substance use policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubstanceUseProgram: Coding = {
  code: 'SUBPRG',
  display: 'substance use program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubsidizedFeeForServiceProgram: Coding = {
  code: 'SUBSIDFFS',
  display: 'subsidized fee for service program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubsidizedHealthProgram: Coding = {
  code: 'SUBSIDIZ',
  display: 'subsidized health program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubsidizedManagedCareProgram: Coding = {
  code: 'SUBSIDMC',
  display: 'subsidized managed care program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CommonSubstanceInteraction: Coding = {
  code: 'SUBSTNCE',
  display: 'Common Substance Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubsidizedSupplementalHealthProgram: Coding = {
  code: 'SUBSUPP',
  display: 'subsidized supplemental health program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SubstanceUseDisorderInformationSensitivity: Coding = {
  code: 'SUD',
  display: 'substance use disorder information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NutritionalSupplement: Coding = {
  code: 'SUPPLEMENT',
  display: 'nutritional supplement',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RecordSuppressed: Coding = {
  code: 'SUPPRESSED',
  display: 'record suppressed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Surgical: Coding = {
  code: 'SURG',
  display: 'Surgical',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_SurplusLineInsurancePolicy: Coding = {
  code: 'SURPL',
  display: 'surplus line insurance policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TeaOnly: Coding = {
  code: 'T',
  display: 'tea only',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrialBalance: Coding = {
  code: 'TB',
  display: 'Trial Balance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Taboo: Coding = {
  code: 'TBOO',
  display: 'taboo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrialBalancePartialStrength: Coding = {
  code: 'TBS',
  display: 'trial balance partial strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Teacher: Coding = {
  code: 'TEACHER',
  display: 'teacher',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrialFill: Coding = {
  code: 'TF',
  display: 'Trial Fill',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrialFillPartialStrength: Coding = {
  code: 'TFS',
  display: 'trial fill partial strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_AbsoluteTimeSequence: Coding = {
  code: 'TIME_ABSOLUTE',
  display: 'absolute time sequence',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_RelativeTimeSequence: Coding = {
  code: 'TIME_RELATIVE',
  display: 'relative time sequence',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Title38Section7332: Coding = {
  code: 'Title38Section7332',
  display: 'Title 38 Section 7332',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TermLifeInsurancePolicy: Coding = {
  code: 'TLIFE',
  display: 'term life insurance policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TransactionFee: Coding = {
  code: 'TRAN',
  display: 'transaction fee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TransmissionFormat: Coding = {
  code: 'TRANF',
  display: 'transmission format',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Transfer: Coding = {
  code: 'TRANSFER',
  display: 'Transfer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Travel: Coding = {
  code: 'TRAVEL',
  display: 'travel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_CommonTravelInteraction: Coding = {
  code: 'TRAVINT',
  display: 'Common Travel Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TransfusionTransmission: Coding = {
  code: 'TRNSFTRNS',
  display: 'transfusion transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrustAccreditation: Coding = {
  code: 'TRSTACCRD',
  display: 'trust accreditation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrustAccreditationObservation: Coding = {
  code: 'TRSTACCRDOBS',
  display: 'trust accreditation observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrustAgreement: Coding = {
  code: 'TRSTAGRE',
  display: 'trust agreement',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrustAgreementObservation: Coding = {
  code: 'TRSTAGREOBS',
  display: 'trust agreement observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrustAssurance: Coding = {
  code: 'TRSTASSUR',
  display: 'trust assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrustCertificate: Coding = {
  code: 'TRSTCERT',
  display: 'trust certificate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrustCertificateObservation: Coding = {
  code: 'TRSTCERTOBS',
  display: 'trust certificate observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrustFramework: Coding = {
  code: 'TRSTFWK',
  display: 'trust framework',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrustFrameworkObservation: Coding = {
  code: 'TRSTFWKOBS',
  display: 'trust framework observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrustAssuranceObservation: Coding = {
  code: 'TRSTLOAOBS',
  display: 'trust assurance observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrustMechanism: Coding = {
  code: 'TRSTMEC',
  display: 'trust mechanism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_TrustMechanismObservation: Coding = {
  code: 'TRSTMECOBS',
  display: 'trust mechanism observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_UnitDose: Coding = {
  code: 'UD',
  display: 'Unit Dose',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_UnitDoseEquivalent: Coding = {
  code: 'UDE',
  display: 'unit dose equivalent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Ultrafiltration: Coding = {
  code: 'UFIL',
  display: 'Ultrafiltration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_UniversalLifeInsurancePolicy: Coding = {
  code: 'ULIFE',
  display: 'universal life insurance policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_UmbrellaLiabilityInsurancePolicy: Coding = {
  code: 'UMBRL',
  display: 'umbrella liability insurance policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_UninsuredMotoristPolicy: Coding = {
  code: 'UNINSMOT',
  display: 'uninsured motorist policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_UnitPrice: Coding = {
  code: 'UNITPRICE',
  display: 'Unit Price',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_UnitQuantity: Coding = {
  code: 'UNITQTY',
  display: 'Unit Quantity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_UnrelatedService: Coding = {
  code: 'UNRELAT',
  display: 'unrelated service',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_UnitedNationsStandardProductsAndServicesClassification: Coding = {
  code: 'UNSPSC',
  display: 'United Nations Standard Products and Services Classification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_UniversalProductCode: Coding = {
  code: 'UPC',
  display: 'Universal Product Code',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_UpgradeSecurityLabel: Coding = {
  code: 'UPGRDLABEL',
  display: 'upgrade security label',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Urgent: Coding = {
  code: 'URGENT',
  display: 'urgent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_NoticeOfUse: Coding = {
  code: 'USE',
  display: 'notice of use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_VaccineProductProblem: Coding = {
  code: 'VAC_PROBLEM',
  display: 'vaccine product problem',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ValidationIssue: Coding = {
  code: 'VALIDAT',
  display: 'validation issue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_VectorBorneTransmission: Coding = {
  code: 'VECTRNS',
  display: 'vector-borne transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_VerbalAuthorization: Coding = {
  code: 'VERBAUTH',
  display: 'verbal authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_VeteranHealthProgram: Coding = {
  code: 'VET',
  display: 'veteran health program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_VerifyPaper: Coding = {
  code: 'VFPAPER',
  display: 'verify paper',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ViolenceInformationSensitivity: Coding = {
  code: 'VIO',
  display: 'violence information sensitivity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_VisionCarePolicy: Coding = {
  code: 'VISPOL',
  display: 'vision care policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_LowValinLeucinIsoleucin: Coding = {
  code: 'VLI',
  display: 'low valin, leucin, isoleucin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ActSpecObsVolumeCode: Coding = {
  code: 'VOLUME',
  display: 'ActSpecObsVolumeCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Virtual: Coding = {
  code: 'VR',
  display: 'virtual',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_VisionDispenseInvoice: Coding = {
  code: 'VRXINV',
  display: 'vision dispense invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Ward: Coding = {
  code: 'W',
  display: 'Ward',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_WaterBorneTransmission: Coding = {
  code: 'WATTRNS',
  display: 'water-borne transmission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_WorkerQuoteSCompensation: Coding = {
  code: 'WCBPOL',
  display: "worker's compensation",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Week: Coding = {
  code: 'WEEK',
  display: 'week',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_WellnessReminderListEntry: Coding = {
  code: 'WELLREMLE',
  display: 'wellness reminder list entry',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_WellnessReminderListReview: Coding = {
  code: 'WELLREMLREV',
  display: 'wellness reminder list review',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_WGHT: Coding = {
  code: 'WGHT',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_WorkInjuryReportAttachment: Coding = {
  code: 'WIATTCH',
  display: 'work injury report attachment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_WorkInteraction: Coding = {
  code: 'WORK2',
  display: 'Work Interaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_WorkplaceAccident: Coding = {
  code: 'WPA',
  display: 'Workplace accident',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_WorkersCompensationProgram: Coding = {
  code: 'WRKCOMP',
  display: '(workers compensation program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_ContainerUnavailable: Coding = {
  code: 'X',
  display: 'Container Unavailable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_XRay: Coding = {
  code: 'XRAY',
  display: 'x-ray',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
const v3_ActCode_Year: Coding = {
  code: 'YEAR',
  display: 'year',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
};
/**
 *  A code specifying the particular kind of Act that the Act-instance represents within its class.  Constraints:
 * The kind of Act (e.g. physical examination, serum potassium, inpatient encounter, charge financial transaction, etc.) is specified with a code from one of several, typically external, coding systems.  The coding system will depend on the class of Act, such as LOINC for observations, etc. Conceptually, the Act.code must be a specialization of the Act.classCode. This is why the structure of ActClass domain should be reflected in the superstructure of the ActCode domain and then individual codes or externally referenced vocabularies subordinated under these domains that reflect the ActClass structure. Act.classCode and Act.code are not modifiers of each other but the Act.code concept should really imply the Act.classCode concept. For a negative example, it is not appropriate to use an Act.code "potassium" together with and Act.classCode for "laboratory observation" to somehow mean "potassium laboratory observation" and then use the same Act.code for "potassium" together with Act.classCode for "medication" to mean "substitution of potassium". This mutually modifying use of Act.code and Act.classCode is not permitted.
 */
export const V3ActCode = {
  /**
   * An account represents a grouping of financial transactions that are tracked and reported together with a single balance. 	 	Examples of account codes (types) are Patient billing accounts (collection of charges), Cost centers; Cash.
   */
  ActAccountCode: v3_ActCode_ActAccountCode,
  /**
   * Includes coded responses that will occur as a result of the adjudication of an electronic invoice at a summary level and provides guidance on interpretation of the referenced adjudication results.
   */
  ActAdjudicationCode: v3_ActCode_ActAdjudicationCode,
  /**
   * Catagorization of grouping criteria for the associated transactions and/or summary (totals, subtotals).
   */
  ActAdjudicationGroupCode: v3_ActCode_ActAdjudicationGroupCode,
  /**
   * Actions to be carried out by the recipient of the Adjudication Result information.
   */
  ActAdjudicationResultActionCode: v3_ActCode_ActAdjudicationResultActionCode,
  /**
   * ActAdministrativeAuthorizationDetectedIssueCode
   */
  ActAdministrativeAuthorizationDetectedIssueCode: v3_ActCode_ActAdministrativeAuthorizationDetectedIssueCode,
  /**
   * Identifies types of detectyed issues for Act class "ALRT" for the administrative and patient administrative acts domains.
   */
  ActAdministrativeDetectedIssueCode: v3_ActCode_ActAdministrativeDetectedIssueCode,
  /**
   * Codes dealing with the management of Detected Issue observations for the administrative and patient administrative acts domains.
   */
  ActAdministrativeDetectedIssueManagementCode: v3_ActCode_ActAdministrativeDetectedIssueManagementCode,
  /**
   * ActAdministrativeRuleDetectedIssueCode
   */
  ActAdministrativeRuleDetectedIssueCode: v3_ActCode_ActAdministrativeRuleDetectedIssueCode,
  /**
   * Definition:An identifying modifier code for healthcare interventions or procedures.
   */
  ActBillableModifierCode: v3_ActCode_ActBillableModifierCode,
  /**
   * Definition: An identifying code for billable services, as opposed to codes for similar services used to identify them for functional purposes.
   */
  ActBillableServiceCode: v3_ActCode_ActBillableServiceCode,
  /**
   * The type of provision(s)  made for reimbursing for the deliver of healthcare services and/or goods provided by a Provider, over a specified period.
   */
  ActBillingArrangementCode: v3_ActCode_ActBillingArrangementCode,
  /**
   * Type of bounded ROI.
   */
  ActBoundedROICode: v3_ActCode_ActBoundedROICode,
  /**
   * Description:The type and scope of responsibility taken-on by the performer of the Act for a specific subject of care.
   */
  ActCareProvision: v3_ActCode_ActCareProvision,
  /**
   * Description: Coded types of attachments included to support a healthcare claim.
   */
  ActClaimAttachmentCategoryCode: v3_ActCode_ActClaimAttachmentCategoryCode,
  /**
   * Specifies the type of agreement between one or more grantor and grantee in which rights and obligations related to one or more shared items of interest are allocated.
   *
   *
   *                            Usage Note: Such agreements may be considered "consent directives" or "contracts" depending on the context, and are considered closely related or synonymous from a legal perspective.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare Privacy Consent Directive permitting or restricting in whole or part the collection, access, use, and disclosure of health information, and any associated handling caveats.
   *                            Healthcare Medical Consent Directive to receive medical procedures after being informed of risks and benefits, thereby reducing the grantee's liability.
   *                            Research Informed Consent for participation in clinical trials and disclosure of health information after being informed of risks and benefits, thereby reducing the grantee's liability.
   *                            Substitute decision maker delegation in which the grantee assumes responsibility to act on behalf of the grantor.
   *                            Contracts in which the agreement requires assent/dissent by the grantor of terms offered by a grantee, a consumer opts out of an "award" system for use of a retailer's marketing or credit card vendor's point collection cards in exchange for allowing purchase tracking and profiling.
   *                            A mobile device or App privacy policy and terms of service to which a user must agree in whole or in part in order to utilize the service.
   *                            Agreements between a client and an authorization server or between an authorization server and a resource operator and/or resource owner permitting or restricting e.g., collection, access, use, and disclosure of information, and any associated handling caveats.
   */
  ActConsentDirective: v3_ActCode_ActConsentDirective,
  /**
   * Definition: The type of consent directive, e.g., to consent or dissent to collect, access, or use in specific ways within an EHRS or for health information exchange; or to disclose  health information  for purposes such as research.
   */
  ActConsentType: v3_ActCode_ActConsentType,
  /**
   * Constrains the ActCode to the domain of Container Registration
   */
  ActContainerRegistrationCode: v3_ActCode_ActContainerRegistrationCode,
  /**
   * An observation form that determines parameters or attributes of an Act. Examples are the settings of a ventilator machine as parameters of a ventilator treatment act; the controls on dillution factors of a chemical analyzer as a parameter of a laboratory observation act; the settings of a physiologic measurement assembly (e.g., time skew) or the position of the body while measuring blood pressure.
   *
   *                         Control variables are forms of observations because just as with clinical observations, the Observation.code determines the parameter and the Observation.value assigns the value. While control variables sometimes can be observed (by noting the control settings or an actually measured feedback loop) they are not primary observations, in the sense that a control variable without a primary act is of no use (e.g., it makes no sense to record a blood pressure position without recording a blood pressure, whereas it does make sense to record a systolic blood pressure without a diastolic blood pressure).
   */
  ActControlVariable: v3_ActCode_ActControlVariable,
  /**
   * Indication of authorization for healthcare service(s) and/or product(s).  If authorization is approved, funds are set aside.
   */
  ActCoverageAuthorizationConfirmationCode: v3_ActCode_ActCoverageAuthorizationConfirmationCode,
  /**
   * Response to an insurance coverage eligibility query or authorization request.
   */
  ActCoverageConfirmationCode: v3_ActCode_ActCoverageConfirmationCode,
  /**
   * Indication of eligibility coverage for healthcare service(s) and/or product(s).
   */
  ActCoverageEligibilityConfirmationCode: v3_ActCode_ActCoverageEligibilityConfirmationCode,
  /**
   * Criteria that are applicable to the authorized coverage.
   */
  ActCoverageLimitCode: v3_ActCode_ActCoverageLimitCode,
  /**
   * Maximum amount paid or maximum number of services/products covered; or maximum amount or number covered during a specified time period under the policy or program.
   */
  ActCoverageQuantityLimitCode: v3_ActCode_ActCoverageQuantityLimitCode,
  /**
   * Definition: Set of codes indicating the type of insurance policy or program that pays for the cost of benefits provided to covered parties.
   */
  ActCoverageTypeCode: v3_ActCode_ActCoverageTypeCode,
  /**
   * Codes representing the types of covered parties that may receive covered benefits under a policy or program.
   */
  ActCoveredPartyLimitCode: v3_ActCode_ActCoveredPartyLimitCode,
  /**
   * Description:The type and scope of legal and/or professional responsibility taken-on by the performer of the Act for a specific subject of care as described by a credentialing agency, i.e. government or non-government agency. Failure in executing this Act may result in loss of credential to the person or organization who participates as performer of the Act. Excludes employment agreements.
   *
   *
   *                            Example:Hospital license; physician license; clinic accreditation.
   */
  ActCredentialedCare: v3_ActCode_ActCredentialedCare,
  /**
   * Description:The type and scope of legal and/or professional responsibility taken-on by the performer of the Act for a specific subject of care as described by an agency for credentialing individuals.
   */
  ActCredentialedCareProvisionPeron: v3_ActCode_ActCredentialedCareProvisionPeron,
  /**
   * Description:The type and scope of legal and/or professional responsibility taken-on by the performer of the Act for a specific subject of care as described by an agency for credentialing programs within organizations.
   */
  ActCredentialedCareProvisionProgram: v3_ActCode_ActCredentialedCareProvisionProgram,
  /**
   * Codes dealing with the management of Detected Issue observations
   */
  ActDetectedIssueManagementCode: v3_ActCode_ActDetectedIssueManagementCode,
  /**
   * Accommodation type.  In Intent mood, represents the accommodation type requested.  In Event mood, represents accommodation assigned/used.  In Definition mood, represents the available accommodation type.
   */
  ActEncounterAccommodationCode: v3_ActCode_ActEncounterAccommodationCode,
  /**
   * Domain provides codes that qualify the ActEncounterClass (ENC)
   */
  ActEncounterCode: v3_ActCode_ActEncounterCode,
  /**
   * Concepts that identify the type or nature of exposure interaction.  Examples include "household", "care giver", "intimate partner", "common space", "common substance", etc. to further describe the nature of interaction.
   */
  ActExposureCode: v3_ActCode_ActExposureCode,
  /**
   * ActFinancialTransactionCode
   */
  ActFinancialTransactionCode: v3_ActCode_ActFinancialTransactionCode,
  /**
   * Definition: Set of codes indicating the type of health insurance policy that covers health services provided to covered parties.  A health insurance policy is a written contract for insurance between the insurance company and the policyholder, and contains pertinent facts about the policy owner (the policy holder), the health insurance coverage, the insured subscribers and dependents, and the insurer.  Health insurance is typically administered in accordance with a plan, which specifies (1) the type of health services and health conditions that will be covered under what circumstances (e.g., exclusion of a pre-existing condition, service must be deemed medically necessary; service must not be experimental; service must provided in accordance with a protocol; drug must be on a formulary; service must be prior authorized; or be a referral from a primary care provider); (2) the type and affiliation of providers (e.g., only allopathic physicians, only in network, only providers employed by an HMO); (3) financial participations required of covered parties (e.g., co-pays, coinsurance, deductibles, out-of-pocket); and (4) the manner in which services will be paid (e.g., under indemnity or fee-for-service health plans, the covered party typically pays out-of-pocket and then file a claim for reimbursement, while health plans that have contractual relationships with providers, i.e., network providers, typically do not allow the providers to bill the covered party for the cost of the service until after filing a claim with the payer and receiving reimbursement).
   */
  ActHealthInsuranceTypeCode: v3_ActCode_ActHealthInsuranceTypeCode,
  /**
   * Set of codes indicating the type of incident or accident.
   */
  ActIncidentCode: v3_ActCode_ActIncidentCode,
  /**
   * Description: The type of health information to which the subject of the information or the subject's delegate consents or dissents.
   */
  ActInformationAccessCode: v3_ActCode_ActInformationAccessCode,
  /**
   * Concepts conveying the context in which authorization given under jurisdictional law, by organizational policy, or by a patient consent directive permits the collection, access, use or disclosure of specified patient health information.
   */
  ActInformationAccessContextCode: v3_ActCode_ActInformationAccessContextCode,
  /**
   * Definition:Indicates the set of information types which may be manipulated or referenced, such as for recommending access restrictions.
   */
  ActInformationCategoryCode: v3_ActCode_ActInformationCategoryCode,
  /**
   * Types of sensitivity policies that apply to Acts.  Act.confidentialityCode is defined in the RIM as "constraints around appropriate disclosure of information about this Act, regardless of mood."
   *
   *
   *                            Usage Note: ActSensitivity codes are used to bind information to an Act.confidentialityCode according to local sensitivity policy so that those confidentiality codes can then govern its handling across enterprises.  Internally to a policy domain, however, local policies guide the access control system on how end users in that policy domain are  able to use information tagged with these sensitivity values.
   */
  ActInformationSensitivityPolicy: v3_ActCode_ActInformationSensitivityPolicy,
  /**
   * Set of codes indicating the type of insurance policy or other source of funds to cover healthcare costs.
   */
  ActInsurancePolicyCode: v3_ActCode_ActInsurancePolicyCode,
  /**
   * Definition: Set of codes indicating the type of insurance policy.  Insurance, in law and economics, is a form of risk management primarily used to hedge against the risk of potential financial loss. Insurance is defined as the equitable transfer of the risk of a potential loss, from one entity to another, in exchange for a premium and duty of care. A policy holder is an individual or an organization enters into a contract with an underwriter which stipulates that, in exchange for payment of a sum of money (a premium), one or more covered parties (insureds) is guaranteed compensation for losses resulting from certain perils under specified conditions.  The underwriter analyzes the risk of loss, makes a decision as to whether the risk is insurable, and prices the premium accordingly.  A policy provides benefits that indemnify or cover the cost of a loss incurred by a covered party, and may include coverage for services required to remediate a loss.  An insurance policy contains pertinent facts about the policy holder, the insurance coverage, the covered parties, and the insurer.  A policy may include exemptions and provisions specifying the extent to which the indemnification clause cannot be enforced for intentional tortious conduct of a covered party, e.g., whether the covered parties are jointly or severably insured.
   *
   *
   *                            Discussion: In contrast to programs, an insurance policy has one or more policy holders, who own the policy.  The policy holder may be the covered party, a relative of the covered party, a partnership, or a corporation, e.g., an employer.  A subscriber of a self-insured health insurance policy is a policy holder.  A subscriber of an employer sponsored health insurance policy is holds a certificate of coverage, but is not a policy holder; the policy holder is the employer.  See CoveredRoleType.
   */
  ActInsuranceTypeCode: v3_ActCode_ActInsuranceTypeCode,
  /**
   * Codes representing a grouping of invoice elements (totals, sub-totals), reported through a Payment Advice or a Statement of Financial Activity (SOFA).  The code can represent summaries by day, location, payee and other cost elements such as bonus, retroactive adjustment and transaction fees.
   */
  ActInvoiceAdjudicationPaymentCode: v3_ActCode_ActInvoiceAdjudicationPaymentCode,
  /**
   * Codes representing adjustments to a Payment Advice such as retroactive, clawback, garnishee, etc.
   */
  ActInvoiceAdjudicationPaymentGroupCode: v3_ActCode_ActInvoiceAdjudicationPaymentGroupCode,
  /**
   * Codes representing a grouping of invoice elements (totals, sub-totals), reported through a Payment Advice or a Statement of Financial Activity (SOFA).  The code can represent summaries by day, location, payee, etc.
   */
  ActInvoiceAdjudicationPaymentSummaryCode: v3_ActCode_ActInvoiceAdjudicationPaymentSummaryCode,
  /**
   * An identifying data string for healthcare products.
   */
  ActInvoiceDetailClinicalProductCode: v3_ActCode_ActInvoiceDetailClinicalProductCode,
  /**
   * An identifying data string for healthcare procedures.
   */
  ActInvoiceDetailClinicalServiceCode: v3_ActCode_ActInvoiceDetailClinicalServiceCode,
  /**
   * Codes representing a service or product that is being invoiced (billed).  The code can represent such concepts as "office visit", "drug X", "wheelchair" and other billable items such as taxes, service charges and discounts.
   */
  ActInvoiceDetailCode: v3_ActCode_ActInvoiceDetailCode,
  /**
   * An identifying data string for A substance used as a medication or in the preparation of medication.
   */
  ActInvoiceDetailDrugProductCode: v3_ActCode_ActInvoiceDetailDrugProductCode,
  /**
   * The billable item codes to identify adjudicator specified components to the total billing of a claim.
   */
  ActInvoiceDetailGenericAdjudicatorCode: v3_ActCode_ActInvoiceDetailGenericAdjudicatorCode,
  /**
   * The detail item codes to identify charges or changes to the total billing of a claim due to insurance rules and payments.
   */
  ActInvoiceDetailGenericCode: v3_ActCode_ActInvoiceDetailGenericCode,
  /**
   * The billable item codes to identify modifications to a billable item charge. As for example after hours increase in the office visit fee.
   */
  ActInvoiceDetailGenericModifierCode: v3_ActCode_ActInvoiceDetailGenericModifierCode,
  /**
   * The billable item codes to identify provider supplied charges or changes to the total billing of a claim.
   */
  ActInvoiceDetailGenericProviderCode: v3_ActCode_ActInvoiceDetailGenericProviderCode,
  /**
   * An identifying data string for medical facility accommodations.
   */
  ActInvoiceDetailPreferredAccommodationCode: v3_ActCode_ActInvoiceDetailPreferredAccommodationCode,
  /**
   * The billable item codes to identify modifications to a billable item charge by a tax factor applied to the amount. As for example 7% provincial sales tax.
   */
  ActInvoiceDetailTaxCode: v3_ActCode_ActInvoiceDetailTaxCode,
  /**
   * Type of invoice element that is used to assist in describing an Invoice that is either submitted for adjudication or for which is returned on adjudication results.
   */
  ActInvoiceElementCode: v3_ActCode_ActInvoiceElementCode,
  /**
   * Identifies the different types of summary information that can be reported by queries dealing with Statement of Financial Activity (SOFA).  The summary information is generally used to help resolve balance discrepancies between providers and payors.
   */
  ActInvoiceElementSummaryCode: v3_ActCode_ActInvoiceElementSummaryCode,
  /**
   * Type of invoice element that is used to assist in describing an Invoice that is either submitted for adjudication or for which is returned on adjudication results.
   *
   *                         Invoice elements of this type signify a grouping of one or more children (detail) invoice elements.  They do not have intrinsic costing associated with them, but merely reflect the sum of all costing for it's immediate children invoice elements.
   */
  ActInvoiceGroupCode: v3_ActCode_ActInvoiceGroupCode,
  /**
   * Type of invoice element that is used to assist in describing an Invoice that is either submitted for adjudication or for which is returned on adjudication results.
   *
   *                         Invoice elements of this type signify a grouping of one or more children (detail) invoice elements.  They do not have intrinsic costing associated with them, but merely reflect the sum of all costing for it's immediate children invoice elements.
   *
   *                         The domain is only specified for an intermediate invoice element group (non-root or non-top level) for an Invoice.
   */
  ActInvoiceInterGroupCode: v3_ActCode_ActInvoiceInterGroupCode,
  /**
   * Includes coded responses that will occur as a result of the adjudication of an electronic invoice at a summary level and provides guidance on interpretation of the referenced adjudication results.
   */
  ActInvoiceOverrideCode: v3_ActCode_ActInvoiceOverrideCode,
  /**
   * Type of invoice element that is used to assist in describing an Invoice that is either submitted for adjudication or for which is returned on adjudication results.
   *
   *                         Invoice elements of this type signify a grouping of one or more children (detail) invoice elements.  They do not have intrinsic costing associated with them, but merely reflect the sum of all costing for it's immediate children invoice elements.
   *
   *                         Codes from this domain reflect the type of Invoice such as Pharmacy Dispense, Clinical Service and Clinical Product.  The domain is only specified for the root (top level) invoice element group for an Invoice.
   */
  ActInvoiceRootGroupCode: v3_ActCode_ActInvoiceRootGroupCode,
  /**
   * Provides codes associated with ActClass value of LIST (working list)
   */
  ActListCode: v3_ActCode_ActListCode,
  /**
   * General category of medical service provided to the patient during their encounter.
   */
  ActMedicalServiceCode: v3_ActCode_ActMedicalServiceCode,
  /**
   * Definition:A collection of concepts that identifies different types of 'duration-based' mediation working lists.
   *
   *
   *                            Examples:"Continuous/Chronic" "Short-Term" and "As Needed"
   */
  ActMedicationTherapyDurationWorkingList: v3_ActCode_ActMedicationTherapyDurationWorkingList,
  /**
   * Identifies types of monitoring programs
   */
  ActMonitoringProtocolCode: v3_ActCode_ActMonitoringProtocolCode,
  /**
   * Description:Concepts representing indications (reasons for clinical action) other than diagnosis and symptoms.
   */
  ActNonObservationIndicationCode: v3_ActCode_ActNonObservationIndicationCode,
  /**
   * ActObservationList
   */
  ActObservationList: v3_ActCode_ActObservationList,
  /**
   * Identifies the type of verification investigation being undertaken with respect to the subject of the verification activity.
   *
   *
   *                            Examples:
   *
   *
   *
   *
   *                               Verification of eligibility for coverage under a policy or program - aka enrolled/covered by a policy or program
   *
   *
   *
   *                               Verification of record - e.g., person has record in an immunization registry
   *
   *
   *
   *                               Verification of enumeration - e.g. NPI
   *
   *
   *
   *                               Verification of Board Certification - provider specific
   *
   *
   *
   *                               Verification of Certification - e.g. JAHCO, NCQA, URAC
   *
   *
   *
   *                               Verification of Conformance - e.g. entity use with HIPAA, conformant to the CCHIT EHR system criteria
   *
   *
   *
   *                               Verification of Provider Credentials
   *
   *
   *
   *                               Verification of no adverse findings - e.g. on National Provider Data Bank, Health Integrity Protection Data Base (HIPDB)
   */
  ActObservationVerification: v3_ActCode_ActObservationVerification,
  /**
   * Description:Provides a categorization for annotations recorded directly against the patient .
   */
  ActPatientAnnotationType: v3_ActCode_ActPatientAnnotationType,
  /**
   * Definition: Characterizes how a patient was or will be transported to the site of a patient encounter.
   *
   *
   *                            Examples: Via ambulance, via public transit, on foot.
   */
  ActPatientTransportationModeCode: v3_ActCode_ActPatientTransportationModeCode,
  /**
   * Code identifying the method or the movement of payment instructions.
   *
   *                         Codes are drawn from X12 data element 591 (PaymentMethodCode)
   */
  ActPaymentCode: v3_ActCode_ActPaymentCode,
  /**
   * Identifies types of dispensing events
   */
  ActPharmacySupplyType: v3_ActCode_ActPharmacySupplyType,
  /**
   * Description:Types of policies that further specify the ActClassPolicy value set.
   */
  ActPolicyType: v3_ActCode_ActPolicyType,
  /**
   * A jurisdictional mandate, regulation, obligation, requirement, rule, or expectation deeming certain information to be private to an individual or organization, which is imposed on:
   *
   *
   *                            The activity of a governed party
   *                            The behavior of a governed party
   *                            The manner in which an act is executed by a governed party
   */
  ActPrivacyLaw: v3_ActCode_ActPrivacyLaw,
  /**
   * A policy deeming certain information to be private to an individual or organization.
   *
   *
   *                            Definition: A mandate, obligation, requirement, rule, or expectation relating to privacy.
   *
   *
   *                            Discussion: ActPrivacyPolicyType codes support the designation of the 1..* policies that are applicable to an Act such as a Consent Directive, a Role such as a VIP Patient, or an Entity such as a patient who is a minor.  1..* ActPrivacyPolicyType values may be associated with an Act or Role to indicate the policies that govern the assignment of an Act or Role confidentialityCode.  Use of multiple ActPrivacyPolicyType values enables fine grain specification of applicable policies, but must be carefully assigned to ensure cogency and avoid creation of conflicting policy mandates.
   *
   *
   *                            Usage Note: Statutory title may be named in the ActClassPolicy Act Act.title to specify which privacy policy is being referenced.
   */
  ActPrivacyPolicy: v3_ActCode_ActPrivacyPolicy,
  /**
   * An identifying code for healthcare interventions/procedures.
   */
  ActProcedureCode: v3_ActCode_ActProcedureCode,
  /**
   * The method that a product is obtained for use by the subject of the supply act (e.g. patient).  Product examples are consumable or durable goods.
   */
  ActProductAcquisitionCode: v3_ActCode_ActProductAcquisitionCode,
  /**
   * Definition: A set of codes used to indicate coverage under a program.  A program is an organized structure for administering and funding coverage of a benefit package for covered parties meeting eligibility criteria, typically related to employment, health, financial, and demographic status. Programs are typically established or permitted by legislation with provisions for ongoing government oversight.  Regulations may mandate the structure of the program, the manner in which it is funded and administered, covered benefits, provider types, eligibility criteria and financial participation. A government agency may be charged with implementing the program in accordance to the regulation.  Risk of loss under a program in most cases would not meet what an underwriter would consider an insurable risk, i.e., the risk is not random in nature, not financially measurable, and likely requires subsidization with government funds.
   *
   *
   *                            Discussion: Programs do not have policy holders or subscribers.  Program eligibles are enrolled based on health status, statutory eligibility, financial status, or age.  Program eligibles who are covered parties under the program may be referred to as members, beneficiaries, eligibles, or recipients.  Programs risk are underwritten by not for profit organizations such as governmental entities, and the beneficiaries typically do not pay for any or some portion of the cost of coverage.  See CoveredPartyRoleType.
   */
  ActProgramTypeCode: v3_ActCode_ActProgramTypeCode,
  /**
   * Transportation of a specimen.
   */
  ActSpecimenTransportCode: v3_ActCode_ActSpecimenTransportCode,
  /**
   * Set of codes related to specimen treatments
   */
  ActSpecimenTreatmentCode: v3_ActCode_ActSpecimenTreatmentCode,
  /**
   * Identifies the type of observation that is made about a specimen that may affect its processing, analysis or further result interpretation
   */
  ActSpecObsCode: v3_ActCode_ActSpecObsCode,
  /**
   * Description: Describes the type of substance administration being performed.  This should not be used to carry codes for identification of products.  Use an associated role or entity to carry such information.
   */
  ActSubstanceAdministrationCode: v3_ActCode_ActSubstanceAdministrationCode,
  /**
   * Identifies types of detected issues regarding the administration or supply of an item to a patient.
   */
  ActSuppliedItemDetectedIssueCode: v3_ActCode_ActSuppliedItemDetectedIssueCode,
  /**
   * Description: A task or action that a user may perform in a clinical information system (e.g., medication order entry, laboratory test results review, problem list entry).
   */
  ActTaskCode: v3_ActCode_ActTaskCode,
  /**
   * Codes used to identify different types of 'duration-based' working lists.  Examples include "Continuous/Chronic", "Short-Term" and "As-Needed".
   */
  ActTherapyDurationWorkingListCode: v3_ActCode_ActTherapyDurationWorkingListCode,
  /**
   * Characterizes how a transportation act was or will be carried out.
   *
   *
   *                            Examples: Via private transport, via public transit, via courier.
   */
  ActTransportationModeCode: v3_ActCode_ActTransportationModeCode,
  /**
   * Definition: A jurisdictional mandate in the U.S. relating to privacy.
   *
   *
   *                            Usage Note: ActPrivacyLaw codes may be associated with an Act or a Role to indicate the legal provision to which the assignment of an Act.confidentialityCode or Role.confidentialtyCode complies.  May be used to further specify rationale for assignment of other ActPrivacyPolicy codes in the US realm, e.g., ETH and 42CFRPart2 can be differentiated from ETH and Title38Part1.
   */
  ActUSPrivacyLaw: v3_ActCode_ActUSPrivacyLaw,
  /**
   * Administration of the proposed therapy may be inappropriate or contraindicated as proposed
   */
  AdministrationDetectedIssueCode: v3_ActCode_AdministrationDetectedIssueCode,
  /**
   * AnnotationType
   */
  AnnotationType: v3_ActCode_AnnotationType,
  /**
   * AppropriatenessDetectedIssueCode
   */
  AppropriatenessDetectedIssueCode: v3_ActCode_AppropriatenessDetectedIssueCode,
  /**
   * Authorization Issue Management Code
   */
  AuthorizationIssueManagementCode: v3_ActCode_AuthorizationIssueManagementCode,
  /**
   * Code for the mechanism by which disease was acquired by the living subject involved in the public health case. Includes sexually transmitted, airborne, bloodborne, vectorborne, foodborne, zoonotic, nosocomial, mechanical, dermal, congenital, environmental exposure, indeterminate.
   */
  CaseTransmissionMode: v3_ActCode_CaseTransmissionMode,
  /**
   * ECGObservationSeriesType
   */
  ECGObservationSeriesType: v3_ActCode_ECGObservationSeriesType,
  /**
   * Types of sensitivity policies that may apply to a sensitive attribute on an Entity.
   *
   *
   *                            Usage Note: EntitySensitivity codes are used to convey a policy that is applicable to sensitive information conveyed by an entity attribute.  May be used to bind a Role.confidentialityCode associated with an Entity per organizational policy.  Role.confidentialityCode is defined in the RIM as "an indication of the appropriate disclosure of information about this Role with respect to the playing Entity."
   */
  EntityInformationSensitivityPolicy: v3_ActCode_EntityInformationSensitivityPolicy,
  /**
   * FDA label data
   */
  FDALabelData: v3_ActCode_FDALabelData,
  /**
   * Description: None provided
   */
  GeneticObservationType: v3_ActCode_GeneticObservationType,
  /**
   * Description:Accommodation type. In Intent mood, represents the accommodation type requested. In Event mood, represents accommodation assigned/used. In Definition mood, represents the available accommodation type.
   */
  HL7AccommodationCode: v3_ActCode_HL7AccommodationCode,
  /**
   * Domain provides the root for HL7-defined detailed or rich codes for the Act classes.
   */
  HL7DefinedActCodes: v3_ActCode_HL7DefinedActCodes,
  /**
   * Description: Observation codes which describe characteristics of the immunization material.
   */
  ImmunizationObservationType: v3_ActCode_ImmunizationObservationType,
  /**
   * A code that is used to indicate the type of case safety report received from sender. The current code example reference is from the International Conference on Harmonisation (ICH) Expert Workgroup guideline on Clinical Safety Data Management: Data Elements for Transmission of Individual Case Safety Reports. The unknown/unavailable option allows the transmission of information from a secondary sender where the initial sender did not specify the type of report.
   *
   *                         Example concepts include: Spontaneous, Report from study, Other.
   */
  IndividualCaseSafetyReportType: v3_ActCode_IndividualCaseSafetyReportType,
  /**
   * A mandate, obligation, requirement, rule, or expectation characterizing the value or importance of a resource and may include its vulnerability. (Based on ISO7498-2:1989. Note: The vulnerability of personally identifiable sensitive information may be based on concerns that the unauthorized disclosure may result in social stigmatization or discrimination.) Description:  Types of Sensitivity policy that apply to Acts or Roles.  A sensitivity policy is adopted by an enterprise or group of enterprises (a 'policy domain') through a formal data use agreement that stipulates the value, importance, and vulnerability of information. A sensitivity code representing a sensitivity policy may be associated with criteria such as categories of information or sets of information identifiers (e.g., a value set of clinical codes or branch in a code system hierarchy).   These criteria may in turn be used for the Policy Decision Point in a Security Engine.  A sensitivity code may be used to set the confidentiality code used on information about Acts and Roles to trigger the security mechanisms required to control how security principals (i.e., a person, a machine, a software application) may act on the information (e.g., collection, access, use, or disclosure). Sensitivity codes are never assigned to the transport or business envelope containing patient specific information being exchanged outside of a policy domain as this would disclose the information intended to be protected by the policy.  When sensitive information is exchanged with others outside of a policy domain, the confidentiality code on the transport or business envelope conveys the receiver's responsibilities and indicates the how the information is to be safeguarded without unauthorized disclosure of the sensitive information.  This ensures that sensitive information is treated by receivers as the sender intends, accomplishing interoperability without point to point negotiations.
   *
   *
   *                            Usage Note: Sensitivity codes are not useful for interoperability outside of a policy domain because sensitivity policies are typically localized and vary drastically across policy domains even for the same information category because of differing organizational business rules, security policies, and jurisdictional requirements.  For example, an employee's sensitivity code would make little sense for use outside of a policy domain.   'Taboo' would rarely be useful outside of a policy domain unless there are jurisdictional requirements requiring that a provider disclose sensitive information to a patient directly.  Sensitivity codes may be more appropriate in a legacy system's Master Files in order to notify those who access a patient's orders and observations about the sensitivity policies that apply.  Newer systems may have a security engine that uses a sensitivity policy's criteria directly.  The specializable InformationSensitivityPolicy Act.code may be useful in some scenarios if used in combination with a sensitivity identifier and/or Act.title.
   */
  InformationSensitivityPolicy: v3_ActCode_InformationSensitivityPolicy,
  /**
   * InteractionDetectedIssueCode
   */
  InteractionDetectedIssueCode: v3_ActCode_InteractionDetectedIssueCode,
  /**
   * Total counts and total net amounts adjudicated for all  Invoice Groupings that were adjudicated within a time period based on the adjudication date of the Invoice Grouping.
   */
  InvoiceElementAdjudicated: v3_ActCode_InvoiceElementAdjudicated,
  /**
   * Total counts and total net amounts paid for all  Invoice Groupings that were paid within a time period based on the payment date.
   */
  InvoiceElementPaid: v3_ActCode_InvoiceElementPaid,
  /**
   * Total counts and total net amounts billed for all Invoice Groupings that were submitted within a time period.  Adjudicated invoice elements are included.
   */
  InvoiceElementSubmitted: v3_ActCode_InvoiceElementSubmitted,
  /**
   * Definition:The set of LOINC codes for the act of determining the period of time that has elapsed since an entity was born or created.
   */
  LOINCObservationActContextAgeType: v3_ActCode_LOINCObservationActContextAgeType,
  /**
   * MedicationObservationType
   */
  MedicationObservationType: v3_ActCode_MedicationObservationType,
  /**
   * Distinguishes the kinds of coded observations that could be the trigger for clinical issue detection. These are observations that are not measurable, but instead can be defined with codes. Coded observation types include: Allergy, Intolerance, Medical Condition, Pregnancy status, etc.
   */
  ObservationIssueTriggerCodedObservationType: v3_ActCode_ObservationIssueTriggerCodedObservationType,
  /**
   * Codes used to define various metadata aspects of a health quality measure.
   */
  ObservationQualityMeasureAttribute: v3_ActCode_ObservationQualityMeasureAttribute,
  /**
   * ObservationSequenceType
   */
  ObservationSequenceType: v3_ActCode_ObservationSequenceType,
  /**
   * ObservationSeriesType
   */
  ObservationSeriesType: v3_ActCode_ObservationSeriesType,
  /**
   * Identifies the kinds of observations that can be performed
   */
  ObservationType: v3_ActCode_ObservationType,
  /**
   * Description: Reporting codes that are related to an immunization event.
   */
  PatientImmunizationRelatedObservationType: v3_ActCode_PatientImmunizationRelatedObservationType,
  /**
   * Observation types for specifying criteria used to assert that a subject is included in a particular population.
   */
  PopulationInclusionObservationType: v3_ActCode_PopulationInclusionObservationType,
  /**
   * Types of observations that can be made about Preferences.
   */
  PreferenceObservationType: v3_ActCode_PreferenceObservationType,
  /**
   * Shape of the region on the object being referenced
   */
  ROIOverlayShape: v3_ActCode_ROIOverlayShape,
  /**
   * Types of sensitivity policies that apply to Roles.
   *
   *
   *                            Usage Notes: RoleSensitivity codes are used to bind information to a Role.confidentialityCode per organizational policy.  Role.confidentialityCode is defined in the RIM as "an indication of the appropriate disclosure of information about this Role with respect to the playing Entity."
   */
  RoleInformationSensitivityPolicy: v3_ActCode_RoleInformationSensitivityPolicy,
  /**
   * Supplying the product at this time may be inappropriate or indicate compliance issues with the associated therapy
   */
  SupplyDetectedIssueCode: v3_ActCode_SupplyDetectedIssueCode,
  /**
   * Proposed therapy may be inappropriate or ineffective based on the proposed start or end time.
   */
  TimingDetectedIssueCode: v3_ActCode_TimingDetectedIssueCode,
  /**
   * Confirmed drug therapy appropriate
   */
  TherapyAppropriate: v3_ActCode_TherapyAppropriate,
  /**
   * Provided education or training to the patient on appropriate therapy use
   */
  ProvidedPatientEducation: v3_ActCode_ProvidedPatientEducation,
  /**
   * Instituted an additional therapy to mitigate potential negative effects
   */
  AddedConcurrentTherapy: v3_ActCode_AddedConcurrentTherapy,
  /**
   * Suspended existing therapy that triggered interaction for the duration of this therapy
   */
  TemporarilySuspendedConcurrentTherapy: v3_ActCode_TemporarilySuspendedConcurrentTherapy,
  /**
   * Aborted existing therapy that triggered interaction.
   */
  StoppedConcurrentTherapy: v3_ActCode_StoppedConcurrentTherapy,
  /**
   * Confirmed supply action appropriate
   */
  SupplyAppropriate: v3_ActCode_SupplyAppropriate,
  /**
   * Patient's existing supply was lost/wasted
   */
  Replacement: v3_ActCode_Replacement,
  /**
   * Supply date is due to patient vacation
   */
  VacationSupply: v3_ActCode_VacationSupply,
  /**
   * Supply date is intended to carry patient over weekend
   */
  WeekendSupply: v3_ActCode_WeekendSupply,
  /**
   * Supply is intended for use during a leave of absence from an institution.
   */
  LeaveOfAbsence: v3_ActCode_LeaveOfAbsence,
  /**
   * Consulted other supplier/pharmacy, therapy confirmed
   */
  ConsultedSupplier: v3_ActCode_ConsultedSupplier,
  /**
   * Assessed patient, therapy is appropriate
   */
  AssessedPatient: v3_ActCode_AssessedPatient,
  /**
   * Description: Supply is different than expected as an additional quantity has been supplied in a separate dispense.
   */
  AdditionalQuantityOnSeparateDispense: v3_ActCode_AdditionalQuantityOnSeparateDispense,
  /**
   * Description: Indicates that the permissions have been externally verified and the request should be processed.
   */
  AuthorizationConfirmed: v3_ActCode_AuthorizationConfirmed,
  /**
   * Definition:Estimated age.
   */
  AgePatientQnEst: v3_ActCode_AgePatientQnEst,
  /**
   * Definition:Reported age.
   */
  AgePatientQnReported: v3_ActCode_AgePatientQnReported,
  /**
   * Description: The patient has the appropriate indication or diagnosis for the action to be taken.
   */
  AppropriateIndicationOrDiagnosis: v3_ActCode_AppropriateIndicationOrDiagnosis,
  /**
   * Description: It has been confirmed that the appropriate pre-requisite therapy has been tried.
   */
  PriorTherapyDocumented: v3_ActCode_PriorTherapyDocumented,
  /**
   * Definition:Calculated age.
   */
  AgePatientQnCalc: v3_ActCode_AgePatientQnCalc,
  /**
   * Patient gave adequate explanation
   */
  PatientExplanation: v3_ActCode_PatientExplanation,
  /**
   * Definition:General specification of age with no implied method of determination.
   */
  AgePatientQnDefinition: v3_ActCode_AgePatientQnDefinition,
  /**
   * Definition:Age at onset of associated adverse event; no implied method of determination.
   */
  AgeAtOnsetOfAdverseEvent: v3_ActCode_AgeAtOnsetOfAdverseEvent,
  /**
   * Consulted other supply source, therapy still appropriate
   */
  ConsultedOtherSource: v3_ActCode_ConsultedOtherSource,
  /**
   * 42 CFR Part 2 stipulates the right of an individual who has applied for or been given diagnosis or treatment for alcohol or drug abuse at a federally assisted program.
   *
   *
   *                            Definition: Non-disclosure of health information relating to health care paid for by a federally assisted substance abuse program without patient consent.
   *
   *
   *                            Usage Note: May be associated with an Act or a Role to indicate the legal provision to which the assignment of an Act.confidentialityCode or Role.confidentialityCode complies.
   */
  VAL42CFRPart2: v3_ActCode_VAL42CFRPart2,
  /**
   * Consulted prescriber, therapy confirmed
   */
  ConsultedPrescriber: v3_ActCode_ConsultedPrescriber,
  /**
   * Consulted prescriber and recommended change, prescriber declined
   */
  PrescriberDeclinedChange: v3_ActCode_PrescriberDeclinedChange,
  /**
   * Concurrent therapy triggering alert is no longer on-going or planned
   */
  InteractingTherapyNoLongerActivePlanned: v3_ActCode_InteractingTherapyNoLongerActivePlanned,
  /**
   * Order is performed as issued, but other action taken to mitigate potential adverse effects
   */
  OtherActionTaken: v3_ActCode_OtherActionTaken,
  /**
   * Arranged to monitor patient for adverse effects
   */
  InstitutedOngoingMonitoringProgram: v3_ActCode_InstitutedOngoingMonitoringProgram,
  /**
   * The invoice element has been accepted for payment but one or more adjustment(s) have been made to one or more invoice element line items (component charges).
   *
   *                         Also includes the concept 'Adjudicate as zero' and items not covered under a particular Policy.
   *
   *                         Invoice element can be reversed (nullified).
   *
   *                         Recommend that the invoice element is saved for DUR (Drug Utilization Reporting).
   */
  AdjudicatedWithAdjustments: v3_ActCode_AdjudicatedWithAdjustments,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the respective accreditation agency.
   */
  AccreditedAssistedLivingCare: v3_ActCode_AccreditedAssistedLivingCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the respective accreditation agency.
   */
  AccreditedAmbulatoryCare: v3_ActCode_AccreditedAmbulatoryCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the respective accreditation agency.
   */
  AccreditedBehavioralHealthCare: v3_ActCode_AccreditedBehavioralHealthCare,
  /**
   * Description:The proposed therapy is frequently misused or abused and therefore should be used with caution and/or monitoring.
   */
  CommonlyAbusedMisusedAlert: v3_ActCode_CommonlyAbusedMisusedAlert,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the respective accreditation agency.
   */
  AccreditedCriticalAccessHospitalCare: v3_ActCode_AccreditedCriticalAccessHospitalCare,
  /**
   * Description: Provide consent to collect, use, disclose, or access adverse drug reaction information for a patient.
   */
  AdverseDrugReactionAccess: v3_ActCode_AdverseDrugReactionAccess,
  /**
   * Description: Provide consent to collect, use, disclose, or access all information for a patient.
   */
  AllAccess: v3_ActCode_AllAccess,
  /**
   * Description: Provide consent to collect, use, disclose, or access allergy information for a patient.
   */
  AllergyAccess: v3_ActCode_AllergyAccess,
  /**
   * An access control policy specific to the type of access control scheme, which is used to enforce one or more authorization policies.
   *
   *
   *                            Usage Note: Access control schemes are the type of access control policy, which is comprised of access control policy rules concerning the provision of the access control service.
   *
   *                         There are two categories of access control policies, rule-based and identity-based, which are identified in CCITT Rec. X.800 aka ISO 7498-2. Rule-based access control policies are intended to apply to all access requests by any initiator on any target in a security domain. Identity-based access control policies are based on rules specific to an individual initiator, a group of initiators, entities acting on behalf of initiators, or originators acting in a specific role. Context can modify rule-based or identity-based access control policies. Context rules may define the entire policy in effect. Real systems will usually employ a combination of these policy types; if a rule-based policy is used, then an identity-based policy is usually in effect also.
   *
   *                         An access control scheme may be based on access control lists, capabilities, labels, and context or a combination of these.  An access control scheme is a component of an access control mechanism or "service") along with the supporting mechanisms required by that scheme to provide access control decision information (ADI) supplied by the scheme to the access decision facility (ADF also known as a PDP). (Based on ISO/IEC 10181-3:1996)
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Attribute Based Access Control (ABAC)
   *                            Discretionary Access Control (DAC)
   *                            History Based Access Control (HBAC)
   *                            Identity Based Access Control (IBAC)
   *                            Mandatory Access Control (MAC)
   *                            Organization Based Access Control (OrBAC)
   *                            Relationship Based Access Control (RelBac)
   *                            Responsibility Based Access Control (RespBAC)
   *                            Risk Adaptable Access Control (RAdAC)
   *                         >
   */
  AccessControlScheme: v3_ActCode_AccessControlScheme,
  /**
   * Description: Provide consent to collect, use, disclose, or access informational consent information for a patient.
   */
  InformationalConsentAccess: v3_ActCode_InformationalConsentAccess,
  /**
   * An account for collecting charges, reversals, adjustments and payments, including deductibles, copayments, coinsurance (financial transactions) credited or debited to the account receivable account for a patient's encounter.
   */
  AccountReceivable: v3_ActCode_AccountReceivable,
  /**
   * Description: Provide consent to collect, use, disclose, or access demographics information for a patient.
   */
  DemographicsAccess: v3_ActCode_DemographicsAccess,
  /**
   * Description: Provide consent to collect, use, disclose, or access diagnostic imaging information for a patient.
   */
  DiagnosticImagingAccess: v3_ActCode_DiagnosticImagingAccess,
  /**
   * Automated Clearing House (ACH).
   */
  AutomatedClearingHouse: v3_ActCode_AutomatedClearingHouse,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the respective accreditation agency.
   */
  AccreditedHospitalCare: v3_ActCode_AccreditedHospitalCare,
  /**
   * The lowering of specimen pH through the addition of an acid
   */
  Acidification: v3_ActCode_Acidification,
  /**
   * Description: Provide consent to collect, use, disclose, or access immunization information for a patient.
   */
  ImmunizationAccess: v3_ActCode_ImmunizationAccess,
  /**
   * Description: Provide consent to collect, use, disclose, or access lab test result information for a patient.
   */
  LabTestResultAccess: v3_ActCode_LabTestResultAccess,
  /**
   * Description: Provide consent to collect, use, disclose, or access medical condition information for a patient.
   */
  MedicationAccess: v3_ActCode_MedicationAccess,
  /**
   * Definition: Provide consent to view or access medical condition information for a patient.
   */
  MedicalConditionAccess: v3_ActCode_MedicalConditionAccess,
  /**
   * Description:Provide consent to collect, use, disclose, or access mental health information for a patient.
   */
  MentalHealthAccess: v3_ActCode_MentalHealthAccess,
  /**
   * Description: Provide consent to collect, use, disclose, or access common observation information for a patient.
   */
  CommonObservationsAccess: v3_ActCode_CommonObservationsAccess,
  /**
   * A group of health care entities, which may include health care providers, care givers, hospitals, facilities, health plans, and other health care constituents who coordinate care for reimbursement based on quality metrics for improving outcomes and lowering costs, and may be authorized to access the consumer's health information because of membership in that group.
   *
   *                         Security Compartment Labels assigned to a consumer's information use in accountable care workflows should be met or exceeded by the Security Compartment attribute claimed by a participant in a an accountable care workflow who is requesting access to that information
   */
  AccountableCareOrganizationCompartment: v3_ActCode_AccountableCareOrganizationCompartment,
  /**
   * Description: Provide consent to collect, use, disclose, or access coverage policy or program for a patient.
   */
  PolicyOrProgramInformationAccess: v3_ActCode_PolicyOrProgramInformationAccess,
  /**
   * Description: Provide consent to collect, use, disclose, or access provider information for a patient.
   */
  ProviderInformationAccess: v3_ActCode_ProviderInformationAccess,
  /**
   * Description: Provide consent to collect, use, disclose, or access professional service information for a patient.
   */
  ProfessionalServiceAccess: v3_ActCode_ProfessionalServiceAccess,
  /**
   * Description:Provide consent to collect, use, disclose, or access substance abuse information for a patient.
   */
  SubstanceAbuseAccess: v3_ActCode_SubstanceAbuseAccess,
  /**
   * A mandate, obligation, requirement, rule, or expectation conveyed as security metadata between senders and receivers required to establish the reliability, authenticity, and trustworthiness of their transactions.
   *
   *                         Trust security metadata are observation made about aspects of trust applicable to an IT resource (data, information object, service, or system capability).
   *
   *                         Trust applicable to IT resources is established and maintained in and among security domains, and may be comprised of observations about the domain's trust authority, trust framework, trust policy, trust interaction rules, means for assessing and monitoring adherence to trust policies, mechanisms that enforce trust, and quality and reliability measures of assurance in those mechanisms. [Based on ISO IEC 10181-1 and NIST SP 800-63-2]
   *
   *                         For example, identity proofing , level of assurance, and Trust Framework.
   */
  TrustPolicy: v3_ActCode_TrustPolicy,
  /**
   * Definition:A list of medications which the patient is only expected to consume for the duration of the current order or limited set of orders and which is not expected to be renewed.
   */
  ShortTermAcute: v3_ActCode_ShortTermAcute,
  /**
   * An acute inpatient encounter.
   */
  InpatientAcute: v3_ActCode_InpatientAcute,
  /**
   * Proposed therapy is outside of the standard practice for an adult patient.
   */
  AdultAlert: v3_ActCode_AdultAlert,
  /**
   * Admitting diagnosis are the diagnoses documented  for administrative purposes as the basis for a hospital admission.
   */
  AdmittingDiagnosis: v3_ActCode_AdmittingDiagnosis,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as payable prior to the specified time period (based on adjudication date), subsequently cancelled in the specified period and submitted electronically.
   */
  AdjudNullifiedPriorPeriodElectronicAmount: v3_ActCode_AdjudNullifiedPriorPeriodElectronicAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as payable prior to the specified time period (based on adjudication date), subsequently cancelled in the specified period and submitted electronically.
   */
  AdjudNullifiedPriorPeriodElectronicCount: v3_ActCode_AdjudNullifiedPriorPeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as payable prior to the specified time period (based on adjudication date), subsequently cancelled in the specified period and submitted manually.
   */
  AdjudNullifiedPriorPeriodManualAmount: v3_ActCode_AdjudNullifiedPriorPeriodManualAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as payable prior to the specified time period (based on adjudication date), subsequently cancelled in the specified period and submitted manually.
   */
  AdjudNullifiedPriorPeriodManualCount: v3_ActCode_AdjudNullifiedPriorPeriodManualCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as payable during the specified time period (based on adjudication date), subsequently nullified in the specified period and submitted electronically.
   */
  AdjudNullifiedSamePeriodElectronicAmount: v3_ActCode_AdjudNullifiedSamePeriodElectronicAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as payable during the specified time period (based on adjudication date), subsequently nullified in the specified period and submitted electronically.
   */
  AdjudNullifiedSamePeriodElectronicCount: v3_ActCode_AdjudNullifiedSamePeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as payable during the specified time period (based on adjudication date), subsequently cancelled in the specified period and submitted manually.
   */
  AdjudNullifiedSamePeriodManualAmount: v3_ActCode_AdjudNullifiedSamePeriodManualAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as payable during the specified time period (based on adjudication date), subsequently cancelled in the specified period and submitted manually.
   */
  AdjudNullifiedSamePeriodManualCount: v3_ActCode_AdjudNullifiedSamePeriodManualCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as payable prior to the specified time period (based on adjudication date) that do not match a specified payee (e.g. pay patient) and submitted electronically.
   */
  AdjudNonPayeePayablePriorPeriodElectronicAmount: v3_ActCode_AdjudNonPayeePayablePriorPeriodElectronicAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as payable prior to the specified time period (based on adjudication date) that do not match a specified payee (e.g. pay patient) and submitted electronically.
   */
  AdjudNonPayeePayablePriorPeriodElectronicCount: v3_ActCode_AdjudNonPayeePayablePriorPeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as payable prior to the specified time period (based on adjudication date) that do not match a specified payee (e.g. pay patient) and submitted manually.
   */
  AdjudNonPayeePayablePriorPeriodManualAmount: v3_ActCode_AdjudNonPayeePayablePriorPeriodManualAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as payable prior to the specified time period (based on adjudication date) that do not match a specified payee (e.g. pay patient) and submitted manually.
   */
  AdjudNonPayeePayablePriorPeriodManualCount: v3_ActCode_AdjudNonPayeePayablePriorPeriodManualCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as payable during the specified time period (based on adjudication date) that do not match a specified payee (e.g. pay patient) and submitted electronically.
   */
  AdjudNonPayeePayableSamePeriodElectronicAmount: v3_ActCode_AdjudNonPayeePayableSamePeriodElectronicAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as payable during the specified time period (based on adjudication date) that do not match a specified payee (e.g. pay patient) and submitted electronically.
   */
  AdjudNonPayeePayableSamePeriodElectronicCount: v3_ActCode_AdjudNonPayeePayableSamePeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as payable during the specified time period (based on adjudication date) that do not match a specified payee (e.g. pay patient) and submitted manually.
   */
  AdjudNonPayeePayableSamePeriodManualAmount: v3_ActCode_AdjudNonPayeePayableSamePeriodManualAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as payable during the specified time period (based on adjudication date) that do not match a specified payee (e.g. pay patient) and submitted manually.
   */
  AdjudNonPayeePayableSamePeriodManualCount: v3_ActCode_AdjudNonPayeePayableSamePeriodManualCount,
  /**
   * Policy for handling information related to an adolescent, which will be afforded heightened confidentiality per applicable organizational or jurisdictional policy.  An enterprise may have a policy that requires that adolescent patient information be provided heightened confidentiality.  Information deemed sensitive typically includes health information and patient role information including patient status, demographics, next of kin, and location.
   *
   *
   *                            Usage Note: For use within an enterprise in which an adolescent is the information subject.  If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  AdolescentInformationSensitivity: v3_ActCode_AdolescentInformationSensitivity,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as payable prior to the specified time period (based on adjudication date) that match a specified payee (e.g. pay provider) and submitted electronically.
   */
  AdjudPayeePayablePriorPeriodElectronicAmount: v3_ActCode_AdjudPayeePayablePriorPeriodElectronicAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as payable prior to the specified time period (based on adjudication date) that match a specified payee (e.g. pay provider) and submitted electronically.
   */
  AdjudPayeePayablePriorPeriodElectronicCount: v3_ActCode_AdjudPayeePayablePriorPeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as payable prior to the specified time period (based on adjudication date) that match a specified payee (e.g. pay provider) and submitted manually.
   */
  AdjudPayeePayablePriorPeriodManualAmout: v3_ActCode_AdjudPayeePayablePriorPeriodManualAmout,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as payable prior to the specified time period (based on adjudication date) that match a specified payee (e.g. pay provider) and submitted manually.
   */
  AdjudPayeePayablePriorPeriodManualCount: v3_ActCode_AdjudPayeePayablePriorPeriodManualCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as payable during the specified time period (based on adjudication date) that match a specified payee (e.g. pay provider) and submitted electronically.
   */
  AdjudPayeePayableSamePeriodElectronicAmount: v3_ActCode_AdjudPayeePayableSamePeriodElectronicAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as payable during the specified time period (based on adjudication date) that match a specified payee (e.g. pay provider) and submitted electronically.
   */
  AdjudPayeePayableSamePeriodElectronicCount: v3_ActCode_AdjudPayeePayableSamePeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as payable during the specified time period (based on adjudication date) that match a specified payee (e.g. pay provider) and submitted manually.
   */
  AdjudPayeePayableSamePeriodManualAmount: v3_ActCode_AdjudPayeePayableSamePeriodManualAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as payable during the specified time period (based on adjudication date) that match a specified payee (e.g. pay provider) and submitted manually.
   */
  AdjudPayeePayableSamePeriodManualCount: v3_ActCode_AdjudPayeePayableSamePeriodManualCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as refused prior to the specified time period (based on adjudication date) and submitted electronically.
   */
  AdjudRefusedPriorPeriodElectronicAmount: v3_ActCode_AdjudRefusedPriorPeriodElectronicAmount,
  /**
   * Identifies the  total number of all  Invoice Groupings that were adjudicated as refused prior to the specified time period (based on adjudication date) and submitted electronically.
   */
  AdjudRefusedPriorPeriodElectronicCount: v3_ActCode_AdjudRefusedPriorPeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as refused prior to the specified time period (based on adjudication date) and submitted manually.
   */
  AdjudRefusedPriorPeriodManualAmount: v3_ActCode_AdjudRefusedPriorPeriodManualAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as refused prior to the specified time period (based on adjudication date) and submitted manually.
   */
  AdjudRefusedPriorPeriodManualCount: v3_ActCode_AdjudRefusedPriorPeriodManualCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as refused during the specified time period (based on adjudication date) and submitted electronically.
   */
  AdjudRefusedSamePeriodElectronicAmount: v3_ActCode_AdjudRefusedSamePeriodElectronicAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as refused during the specified time period (based on adjudication date) and submitted electronically.
   */
  AdjudRefusedSamePeriodElectronicCount: v3_ActCode_AdjudRefusedSamePeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were adjudicated as refused during the specified time period (based on adjudication date) and submitted manually.
   */
  AdjudRefusedSamePeriodManualAmount: v3_ActCode_AdjudRefusedSamePeriodManualAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were adjudicated as refused during the specified time period (based on adjudication date) and submitted manually.
   */
  AdjudRefusedSamePeriodManualCount: v3_ActCode_AdjudRefusedSamePeriodManualCount,
  /**
   * Indicates that the observation is of an unexpected negative occurrence in the subject suspected to result from the subject's exposure to one or more agents.  Observation values would be the symptom resulting from the reaction.
   */
  AdverseReaction: v3_ActCode_AdverseReaction,
  /**
   * American Express
   */
  AmericanExpress: v3_ActCode_AmericanExpress,
  /**
   * pedestrian transport
   */
  PedestrianTransport: v3_ActCode_PedestrianTransport,
  /**
   * Premium paid on service fees in compensation for practicing outside of normal working hours.
   */
  NonNormalHours: v3_ActCode_NonNormalHours,
  /**
   * Proposed therapy may be inappropriate or contraindicated due to patient age
   */
  AgeAlert: v3_ActCode_AgeAlert,
  /**
   * Indicates that the observation is carrying out an aggregation calculation, contained in the value element.
   */
  AggregateMeasureObservation: v3_ActCode_AggregateMeasureObservation,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the respective accreditation agency.
   */
  AccreditedHomeCare: v3_ActCode_AccreditedHomeCare,
  /**
   * Communication of an agent from a living subject or environmental source to a living subject through indirect contact via oral or nasal inhalation.
   */
  AirborneTransmission: v3_ActCode_AirborneTransmission,
  /**
   * Provision of Alternate Level of Care to a patient in an acute bed.  Patient is waiting for placement in a long-term care facility and is unable to return home.
   */
  AlternativeLevelOfCare: v3_ActCode_AlternativeLevelOfCare,
  /**
   * Payment initiated by the payor as the result of adjudicating a submitted invoice that arrived to the payor from an electronic source that did not provide a conformant set of HL7 messages (e.g. web claim submission).
   */
  AlternateElectronic: v3_ActCode_AlternateElectronic,
  /**
   * Hypersensitivity to an agent caused by an immunologic response to an initial exposure
   */
  Allergy: v3_ActCode_Allergy,
  /**
   * Proposed therapy may be inappropriate or contraindicated because of a recorded patient allergy to the proposed product.  (Allergies are immune based reactions.)
   */
  AllergyAlert: v3_ActCode_AllergyAlert,
  /**
   * The act rendering alkaline by impregnating with an alkali; a conferring of alkaline qualities.
   */
  Alkalization: v3_ActCode_Alkalization,
  /**
   * Description: All patient information.
   */
  AllCategories: v3_ActCode_AllCategories,
  /**
   * Definition:The requested action has already been performed and so this request has no effect
   */
  AlreadyPerformed: v3_ActCode_AlreadyPerformed,
  /**
   * Description: A person enters a known allergy for a given patient.
   */
  AllergyListEntry: v3_ActCode_AllergyListEntry,
  /**
   * Description: A person reviews a list of known allergies of a given patient.
   */
  AllergyListReview: v3_ActCode_AllergyListReview,
  /**
   * Definition:All information pertaining to a patient's allergy and intolerance records.
   */
  AllergyCategory: v3_ActCode_AllergyCategory,
  /**
   * Definition:Proposed therapy may be inappropriate or ineffective because the end of administration is too close to another planned therapy.
   */
  EndTooLateAlert: v3_ActCode_EndTooLateAlert,
  /**
   * Definition:Proposed therapy may be inappropriate or ineffective because the start of administration is too late after the onset of the condition.
   */
  StartTooLateAlert: v3_ActCode_StartTooLateAlert,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the respective accreditation agency.
   */
  AccreditedLongTermCare: v3_ActCode_AccreditedLongTermCare,
  /**
   * A comprehensive term for health care provided in a healthcare facility (e.g. a practitioneraTMs office, clinic setting, or hospital) on a nonresident basis. The term ambulatory usually implies that the patient has come to the location and is not assigned to a bed. Sometimes referred to as an outpatient encounter.
   */
  Ambulatory: v3_ActCode_Ambulatory,
  /**
   * fixed-wing ambulance transport
   */
  FixedWingAmbulanceTransport: v3_ActCode_FixedWingAmbulanceTransport,
  /**
   * ground ambulance transport
   */
  GroundAmbulanceTransport: v3_ActCode_GroundAmbulanceTransport,
  /**
   * helicopter ambulance transport
   */
  HelicopterAmbulanceTransport: v3_ActCode_HelicopterAmbulanceTransport,
  /**
   * ambulance transport
   */
  AmbulanceTransport: v3_ActCode_AmbulanceTransport,
  /**
   * Communication of an agent from one animal to another proximate animal.
   */
  AnimalToAnimalTransmission: v3_ActCode_AnimalToAnimalTransmission,
  /**
   * The invoice element has been accepted for payment but one or more adjustment(s) have been made to one or more invoice element line items (component charges) without changing the amount.
   *
   *                         Invoice element can be reversed (nullified).
   *
   *                         Recommend that the invoice element is saved for DUR (Drug Utilization Reporting).
   */
  AdjudicatedWithAdjustmentsAndNoFinancialImpact: v3_ActCode_AdjudicatedWithAdjustmentsAndNoFinancialImpact,
  /**
   * Communication of an agent from an animal to a proximate person.
   */
  AnimalToHumanTransmission: v3_ActCode_AnimalToHumanTransmission,
  /**
   * Description:A note that is specific to a patient's diagnostic images, either historical, current or planned.
   */
  DiagnosticImageNote: v3_ActCode_DiagnosticImageNote,
  /**
   * Description:A general or uncategorized note.
   */
  GeneralNote: v3_ActCode_GeneralNote,
  /**
   * A note that is specific to a patient's immunizations, either historical, current or planned.
   */
  ImmunizationNote: v3_ActCode_ImmunizationNote,
  /**
   * Description:A note that is specific to a patient's laboratory results, either historical, current or planned.
   */
  LaboratoryNote: v3_ActCode_LaboratoryNote,
  /**
   * Description:A note that is specific to a patient's medications, either historical, current or planned.
   */
  MedicationNote: v3_ActCode_MedicationNote,
  /**
   * Definition: A policy that, after an initial premium or premiums, pays out a sum at pre-determined intervals.
   *
   *                         For example, a policy holder may pay $10,000, and in return receive $150 each month until he dies; or $1,000 for each of 14 years or death benefits if he dies before the full term of the annuity has elapsed.
   */
  AnnuityPolicy: v3_ActCode_AnnuityPolicy,
  /**
   * Custodian system must remove any information that could result in identifying the information subject.
   */
  Anonymize: v3_ActCode_Anonymize,
  /**
   * Custodian system must make available to an information subject upon request an accounting of certain disclosures of the individualâ€™s protected health information over a period of time.  Policy may dictate that the accounting include information about the information disclosed,  the date of disclosure, the identification of the receiver, the purpose of the disclosure, the time in which the disclosing entity must provide a response and the time period for which accountings of disclosure can be requested.
   */
  AccountingOfDisclosure: v3_ActCode_AccountingOfDisclosure,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the respective accreditation agency.
   */
  AccreditedOfficeBasedSurgeryCare: v3_ActCode_AccreditedOfficeBasedSurgeryCare,
  /**
   * The invoice element has passed through the adjudication process but payment is refused due to one or more reasons.
   *
   *                         Includes items such as patient not covered, or invoice element is not constructed according to payer rules (e.g. 'invoice submitted too late').
   *
   *                         If one invoice element line item in the invoice element structure is rejected, the remaining line items may not be adjudicated and the complete group is treated as rejected.
   *
   *                         A refused invoice element can be forwarded to the next payer (for Coordination of Benefits) or modified and resubmitted to refusing payer.
   *
   *                         Invoice element cannot be reversed (nullified) as there is nothing to reverse.
   *
   *                         Recommend that the invoice element is not saved for DUR (Drug Utilization Reporting).
   */
  AdjudicatedAsRefused: v3_ActCode_AdjudicatedAsRefused,
  /**
   * Description: All information pertaining to a patient's adverse drug reactions.
   */
  AdverseDrugReactionCategory: v3_ActCode_AdverseDrugReactionCategory,
  /**
   * Describes the artificial blood identifier that is associated with the specimen.
   */
  ActSpecObsArtBldCode: v3_ActCode_ActSpecObsArtBldCode,
  /**
   * The invoice element was/will be paid exactly as submitted, without financial adjustment(s).
   *
   *                         If the dollar amount stays the same, but the billing codes have been amended or financial adjustments have been applied through the adjudication process, the invoice element is treated as "Adjudicated with Adjustment".
   *
   *                         If information items are included in the adjudication results that do not affect the monetary amounts paid, then this is still Adjudicated as Submitted (e.g. 'reached Plan Maximum on this Claim').
   *
   *                         Invoice element can be reversed (nullified).
   *
   *                         Recommend that the invoice element is saved for DUR (Drug Utilization Reporting).
   */
  AdjudicatedAsSubmitted: v3_ActCode_AdjudicatedAsSubmitted,
  /**
   * Description:Refines classCode OBS to indicate an observation in which observation.value contains a finding or other nominalized statement, where the encoded information in Observation.value is not altered by Observation.code.  For instance, observation.code="ASSERTION" and observation.value="fracture of femur present" is an assertion of a clinical finding of femur fracture.
   */
  Assertion: v3_ActCode_Assertion,
  /**
   * Custodian system must monitor systems to ensure that all users are authorized to operate on information objects.
   */
  Audit: v3_ActCode_Audit,
  /**
   * Custodian system must monitor and maintain retrievable log for each user and operation on information.
   */
  AuditTrail: v3_ActCode_AuditTrail,
  /**
   * Authorization approved and funds have been set aside to pay for specified healthcare service(s) and/or product(s) within defined criteria for the authorization.
   */
  Authorized: v3_ActCode_Authorized,
  /**
   * Authorisation policies are essentially security policies related to access-control and specify what activities a subject is permitted or forbidden to do, to a set of target objects. They are designed to protect target objects so are interpreted by access control agents or the run-time systems at the target system.
   *
   *                         A positive authorisation policy defines the actions that a subject is permitted to perform on a target. A negative authorisation policy specifies the actions that a subject is forbidden to perform on a target. Positive authorisation policies may also include filters to transform the parameters associated with their actions.  (Based on PONDERS)
   */
  AuthorizationPolicy: v3_ActCode_AuthorizationPolicy,
  /**
   * Specifies whether or not automatic repeat testing is to be initiated on specimens.
   */
  AutoRepeatPermission: v3_ActCode_AutoRepeatPermission,
  /**
   * Description: Automobile Information Attachment
   */
  AutoAttachment: v3_ActCode_AutoAttachment,
  /**
   * The dilution of a sample performed by automated equipment.  The value is specified by the equipment
   */
  AutoHighDilution: v3_ActCode_AutoHighDilution,
  /**
   * The dilution of a sample performed by automated equipment.  The value is specified by the equipment
   */
  AutoLowDilution: v3_ActCode_AutoLowDilution,
  /**
   * Insurance policy for injuries sustained in an automobile accident.  Will also typically covered non-named parties to the policy, such as pedestrians 	and passengers.
   */
  Automobile: v3_ActCode_Automobile,
  /**
   * The available quantity of specimen.   This is the current quantity minus any planned consumption (e.g., tests that are planned)
   */
  AvailableVolume: v3_ActCode_AvailableVolume,
  /**
   * Policy for handling trade secrets such as financial information or intellectual property, which will be afforded heightened confidentiality.  Description:  Since the service class can represent knowledge structures that may be considered a trade or business secret, there is sometimes (though rarely) the need to flag those items as of business level confidentiality.
   *
   *
   *                            Usage Notes: No patient related information may ever be of this confidentiality level.   If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  BusinessInformationSensitivity: v3_ActCode_BusinessInformationSensitivity,
  /**
   * Communication of an agent from one living subject to another living subject through direct contact with any body fluid.
   */
  BodyFluidContactTransmission: v3_ActCode_BodyFluidContactTransmission,
  /**
   * Policy for handling information related to behavioral and emotional disturbances affecting social adjustment and physical health, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  BehavioralHealthInformationSensitivity: v3_ActCode_BehavioralHealthInformationSensitivity,
  /**
   * Communication of an agent to a living subject through direct contact with blood or blood products whether the contact with blood is part of  a therapeutic procedure or not.
   */
  BloodBorneTransmission: v3_ActCode_BloodBorneTransmission,
  /**
   * A billing arrangement where a Provider charges a lump sum to provide a prescribed group (volume) of services to a single patient which occur over a period of time.  Services included in the block may vary.
   *
   *                         This billing arrangement is also known as Program of Care for some specific Payors and Program Fees for other Payors.
   */
  BlockFunding: v3_ActCode_BlockFunding,
  /**
   * Bonus payments based on performance, volume, etc. as agreed to by the payor.
   */
  Bonus: v3_ActCode_Bonus,
  /**
   * An additional immunization administration within a series intended to bolster or enhance immunity.
   */
  BoosterImmunization: v3_ActCode_BoosterImmunization,
  /**
   * A diet exclusively composed of oatmeal, semolina, or rice, to be extremely easy to eat and digest.
   */
  BreikostGE: v3_ActCode_BreikostGE,
  /**
   * Description:A local business rule relating multiple elements has been violated.
   */
  BusinessConstraintViolation: v3_ActCode_BusinessConstraintViolation,
  /**
   * Description:Indicates that result data has been corrected.
   */
  Corrected: v3_ActCode_Corrected,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedAnatomicPathologyAndClinicalPathologyCare: v3_ActCode_CertifiedAnatomicPathologyAndClinicalPathologyCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedAcuteCoronarySyndromeCare: v3_ActCode_CertifiedAcuteCoronarySyndromeCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedAllergyAndImmunologyCare: v3_ActCode_CertifiedAllergyAndImmunologyCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedAerospaceMedicineCare: v3_ActCode_CertifiedAerospaceMedicineCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedAcuteMyocardialInfarctionCare: v3_ActCode_CertifiedAcuteMyocardialInfarctionCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedAnesthesiologyCare: v3_ActCode_CertifiedAnesthesiologyCare,
  /**
   * A charge to compensate the provider when a patient cancels an appointment with insufficient time for the provider to make another appointment with another patient.
   */
  CancelledAppointment: v3_ActCode_CancelledAppointment,
  /**
   * Definition: A program that provides low-income, uninsured, and underserved women access to timely, high-quality screening and diagnostic services, to detect breast and cervical cancer at the earliest stages.
   *
   *
   *                            Example: To improve women's access to screening for breast and cervical cancers, Congress passed the Breast and Cervical Cancer Mortality Prevention Act of 1990, which guided CDC in creating the National Breast and Cervical Cancer Early Detection Program (NBCCEDP), which  provides access to critical breast and cervical cancer screening services for underserved women in the United States.  An estimated 7 to 10% of U.S. women of screening age are eligible to receive NBCCEDP services. Federal guidelines establish an eligibility baseline to direct services to uninsured and underinsured women at or below 250% of federal poverty level; ages 18 to 64 for cervical screening; ages 40 to 64 for breast screening.
   */
  WomenQuoteSCancerDetectionProgram: v3_ActCode_WomenQuoteSCancerDetectionProgram,
  /**
   * A billing arrangement where the payment made to a Provider is determined by analyzing one or more demographic attributes about the persons/patients who are enrolled with the Provider (in their practice).
   */
  CapitationFunding: v3_ActCode_CapitationFunding,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedAnatomicPathologyCare: v3_ActCode_CertifiedAnatomicPathologyCare,
  /**
   * Provision of diagnosis and treatment of diseases and disorders affecting the heart
   */
  Cardiology: v3_ActCode_Cardiology,
  /**
   * List of acts representing a care plan.  The acts can be in a varierty of moods including event (EVN) to record acts that have been carried out as part of the care plan.
   */
  CarePlan: v3_ActCode_CarePlan,
  /**
   * Definition:An observation that provides a characterization of the level of harm to an investigation subject as a result of a reaction or event.
   */
  CaseSeriousnessCriteria: v3_ActCode_CaseSeriousnessCriteria,
  /**
   * Cash
   */
  Cash: v3_ActCode_Cash,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedAsthmaCare: v3_ActCode_CertifiedAsthmaCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedBariatricSurgeryCare: v3_ActCode_CertifiedBariatricSurgeryCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedClinicalBiochemicalGeneticsCare: v3_ActCode_CertifiedClinicalBiochemicalGeneticsCare,
  /**
   * Description: Types of advance payment to be made on a plastic card usually issued by a financial institution used of purchasing services and/or products.
   */
  CreditCard: v3_ActCode_CreditCard,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedCoronaryArteryDiseaseCare: v3_ActCode_CertifiedCoronaryArteryDiseaseCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedCardiacCare: v3_ActCode_CertifiedCardiacCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedClinicalCytogeneticsCare: v3_ActCode_CertifiedClinicalCytogeneticsCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedClinicalGeneticsMDCare: v3_ActCode_CertifiedClinicalGeneticsMDCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedClinicalPathologyCare: v3_ActCode_CertifiedClinicalPathologyCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedColonAndRectalSurgeryCare: v3_ActCode_CertifiedColonAndRectalSurgeryCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedDermatologyCare: v3_ActCode_CertifiedDermatologyCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedDepressionCare: v3_ActCode_CertifiedDepressionCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedDigestiveGastrointestinalDisordersCare: v3_ActCode_CertifiedDigestiveGastrointestinalDisordersCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedDiabetesCare: v3_ActCode_CertifiedDiabetesCare,
  /**
   * An observation that states whether the disease was likely acquired outside the jurisdiction of observation, and if so, the nature of the inter-jurisdictional relationship.
   *
   *
   *                            OpenIssue: This code could be moved to LOINC if it can be done before there are significant implemenations using it.
   */
  CaseDiseaseImportedObservation: v3_ActCode_CaseDiseaseImportedObservation,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedDiagnosticRadiologyCare: v3_ActCode_CertifiedDiagnosticRadiologyCare,
  /**
   * A person reviews a recommendation/assessment provided automatically by a clinical decision support application for a given patient.
   */
  ClinicalDecisionSupportInterventionReview: v3_ActCode_ClinicalDecisionSupportInterventionReview,
  /**
   * Policy for handling information related to a celebrity (people of public interest (VIP), which will be afforded heightened confidentiality.  Celebrities are people of public interest (VIP) about whose information an enterprise may have a policy that requires heightened confidentiality.  Information deemed sensitive may include health information and patient role information including patient status, demographics, next of kin, and location.
   *
   *
   *                            Usage Note:  For use within an enterprise in which the information subject is deemed a celebrity or very important person.  If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  CelebrityInformationSensitivity: v3_ActCode_CelebrityInformationSensitivity,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedEmergencyMedicineCare: v3_ActCode_CertifiedEmergencyMedicineCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedEpilepsyCare: v3_ActCode_CertifiedEpilepsyCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedFrailElderlyCare: v3_ActCode_CertifiedFrailElderlyCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedFamilyPracticeCare: v3_ActCode_CertifiedFamilyPracticeCare,
  /**
   * An amount still owing to the payor but the payment is 0$ and this cannot be settled until a future payment is made.
   */
  CarryForwardAdjusment: v3_ActCode_CarryForwardAdjusment,
  /**
   * Definition: A program that covers the cost of services provided directly to a beneficiary who typically has no other source of coverage without charge.
   */
  CharityProgram: v3_ActCode_CharityProgram,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedHeartFailureCare: v3_ActCode_CertifiedHeartFailureCare,
  /**
   * A written order to a bank to pay the amount specified from funds on deposit.
   */
  Cheque: v3_ActCode_Cheque,
  /**
   * Description: Exposure participants' interaction occurred in a child care setting
   */
  DayCareChildCareInteraction: v3_ActCode_DayCareChildCareInteraction,
  /**
   * Provision of recurring care for chronic illness.
   */
  Chronic: v3_ActCode_Chronic,
  /**
   * A type of transaction that represents a charge for a service or product.  Expressed in monetary terms.
   */
  StandardCharge: v3_ActCode_StandardCharge,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedHighRiskObstetricsCare: v3_ActCode_CertifiedHighRiskObstetricsCare,
  /**
   * Definition:A list of medications which are expected to be continued beyond the present order and which the patient should be assumed to be taking unless explicitly stopped.
   */
  ContinuousChronic: v3_ActCode_ContinuousChronic,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedHyperlipidemiaCare: v3_ActCode_CertifiedHyperlipidemiaCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedInternalMedicineCare: v3_ActCode_CertifiedInternalMedicineCare,
  /**
   * A circle defined by two (column,row) pairs. The first point is the center of the circle and the second point is a point on the perimeter of the circle.
   */
  Circle: v3_ActCode_Circle,
  /**
   * A clinician enters a clinical note about a given patient
   */
  ClinicalNoteEntryTask: v3_ActCode_ClinicalNoteEntryTask,
  /**
   * A person reviews a clinical note of a given patient.
   */
  ClinicalNoteReviewTask: v3_ActCode_ClinicalNoteReviewTask,
  /**
   * Description: The class room associated with the patient during the immunization event.
   */
  Classroom: v3_ActCode_Classroom,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedClinicalMolecularGeneticsCare: v3_ActCode_CertifiedClinicalMolecularGeneticsCare,
  /**
   * Description:.
   */
  CertifiedMigraineHeadacheCare: v3_ActCode_CertifiedMigraineHeadacheCare,
  /**
   * Indicates what method is used in a quality measure to combine the component measure results included in an composite measure.
   */
  CompositeMeasureMethod: v3_ActCode_CompositeMeasureMethod,
  /**
   * An attribute of a quality measure describing the weight this component measure score is to carry in determining the overall composite measure final score. The value is real value greater than 0 and less than 1.0. Each component measure score will be multiplied by its CMPMSRSCRWGHT and then summed with the other component measures to determine the final overall composite measure score. The sum across all CMPMSRSCRWGHT values within a single composite measure SHALL be 1.0. The value assigned is scoped to the composite measure referencing this component measure only.
   */
  ComponentMeasureScoringWeight: v3_ActCode_ComponentMeasureScoringWeight,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedMultipleSclerosisCare: v3_ActCode_CertifiedMultipleSclerosisCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board
   */
  CertifiedNeurologyCare: v3_ActCode_CertifiedNeurologyCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedNuclearMedicineCare: v3_ActCode_CertifiedNuclearMedicineCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedNeurologyWithSpecialQualificationsInChildNeurologyCare: v3_ActCode_CertifiedNeurologyWithSpecialQualificationsInChildNeurologyCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedNeurologicalSurgeryCare: v3_ActCode_CertifiedNeurologicalSurgeryCare,
  /**
   * Definition:All information pertaining to a patient's common observation records (height, weight, blood pressure, temperature, etc.).
   */
  CommonObservationCategory: v3_ActCode_CommonObservationCategory,
  /**
   * Description:The specified code has been deprecated and should no longer be used.  Select another code from the code system.
   */
  CodeHasBeenDeprecated: v3_ActCode_CodeHasBeenDeprecated,
  /**
   * Description:The specified code is not valid against the list of codes allowed for the element.
   */
  CodeIsNotValid: v3_ActCode_CodeIsNotValid,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedObstetricsAndGynecologyCare: v3_ActCode_CertifiedObstetricsAndGynecologyCare,
  /**
   * Policy for handling information related to cognitive disability disorders and conditions caused by these disorders, which are afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   *
   *                         Examples may include dementia, traumatic brain injury, attention deficit, hearing and visual disability such as dyslexia and other disorders and related conditions which impair learning and self-sufficiency.  However, the cognitive disabilities to which this term may apply  versus other behavioral health categories varies by jurisdiction and organizational policy in part due to overlap with other behavioral health conditions. Implementers should constrain to those diagnoses applicable in the domain in which this code is used.
   */
  CognitiveDisabilityInformationSensitivity: v3_ActCode_CognitiveDisabilityInformationSensitivity,
  /**
   * That portion of the eligible charges which a covered party must pay for each service and/or product. It is a percentage of the eligible amount for the service/product that is typically charged after the covered party has met the policy deductible.  This amount represents the covered party's coinsurance that is applied to a particular adjudication result. It is expressed as a negative dollar amount in adjudication results.
   */
  Coinsurance: v3_ActCode_Coinsurance,
  /**
   * The covered party pays a percentage of the cost of covered services.
   */
  CoInsurance: v3_ActCode_CoInsurance,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedOrthopedicJointReplacementCare: v3_ActCode_CertifiedOrthopedicJointReplacementCare,
  /**
   * Definition: An automobile insurance policy under which the insurance company will cover the cost of damages to an automobile owned by the named insured that are caused by accident or intentionally by another party.
   */
  CollisionCoveragePolicy: v3_ActCode_CollisionCoveragePolicy,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedOccupationalMedicineCare: v3_ActCode_CertifiedOccupationalMedicineCare,
  /**
   * U.S. Federal regulations governing the protection of human subjects in research (codified at Subpart A of 45 CFR part 46) that has been adopted by 15 U.S. Federal departments and agencies in an effort to promote uniformity, understanding, and compliance with human subject protections. Existing regulations governing the protection of human subjects in Food and Drug Administration (FDA)-regulated research (21 CFR parts 50, 56, 312, and 812) are separate from the Common Rule but include similar requirements.
   *
   *
   *                            Definition: U.S. federal laws governing research-related privacy policies.
   *
   *
   *                            Usage Note: May be associated with an Act or a Role to indicate the legal provision to which the assignment of an Act.confidentialityCode or Role.confidentialtyCode complies.
   */
  CommonRule: v3_ActCode_CommonRule,
  /**
   * There may be an issue with the patient complying with the intentions of the proposed therapy
   */
  ComplianceAlert: v3_ActCode_ComplianceAlert,
  /**
   * This is the healthcare analog to the US Intelligence Community's concept of a Special Access Program.  Compartment codes may be used in as a field value in an initiator's clearance to indicate permission to access and use an IT Resource with a security label having the same compartment value in security category label field.
   *
   *                         Map: Aligns with ISO 2382-8 definition of Compartment - "A division of data into isolated blocks with separate security controls for the purpose of reducing risk."
   */
  Compartment: v3_ActCode_Compartment,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedOncologyCare: v3_ActCode_CertifiedOncologyCare,
  /**
   * Proposed therapy may be inappropriate or contraindicated due to an existing/recent patient condition or diagnosis
   */
  ConditionAlert: v3_ActCode_ConditionAlert,
  /**
   * List of condition observations.
   */
  ConditionList: v3_ActCode_ConditionList,
  /**
   * The quantity of specimen that is used each time the equipment uses this substance
   */
  ConsumptionVolume: v3_ActCode_ConsumptionVolume,
  /**
   * Transaction counts and value totals by Contract Identifier.
   */
  Contract: v3_ActCode_Contract,
  /**
   * A billing arrangement where a Provider charges a lump sum to provide a particular volume of one or more interventions/procedures or groups of interventions/procedures.
   */
  ContractFunding: v3_ActCode_ContractFunding,
  /**
   * Description: An interaction where the exposure participants traveled in/on the same vehicle (not necessarily concurrently, e.g. both are passengers of the same plane, but on different flights of that plane).
   */
  CommonConveyanceInteraction: v3_ActCode_CommonConveyanceInteraction,
  COPAY: v3_ActCode_COPAY,
  /**
   * That portion of the eligible charges which a covered party must pay for each service and/or product. It is a defined amount per service/product of the eligible amount for the service/product. This amount represents the covered party's copayment that is applied to a particular adjudication result. It is expressed as a negative dollar amount in adjudication results.
   */
  PatientCoPay: v3_ActCode_PatientCoPay,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedOphthalmologyCare: v3_ActCode_CertifiedOphthalmologyCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedChronicObstructivePulmonaryDiseaseCare: v3_ActCode_CertifiedChronicObstructivePulmonaryDiseaseCare,
  /**
   * Identifies the organization(s) who own the intellectual property represented by the eMeasure.
   */
  Copyright: v3_ActCode_Copyright,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedOrganTransplantCare: v3_ActCode_CertifiedOrganTransplantCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedOrthopaedicSurgeryCare: v3_ActCode_CertifiedOrthopaedicSurgeryCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedOtolaryngologyCare: v3_ActCode_CertifiedOtolaryngologyCare,
  /**
   * Insurance coverage problems have been encountered. Additional explanation information to be supplied.
   */
  CoverageProblem: v3_ActCode_CoverageProblem,
  /**
   * Definition: Codes representing the maximum coverate or financial participation requirements.
   */
  CoverageMaximum: v3_ActCode_CoverageMaximum,
  /**
   * Description:A mandate, obligation, requirement, rule, or expectation unilaterally imposed on benefit coverage under a policy or program by a sponsor, underwriter or payor on:
   *
   *
   *
   *                               The activity of another party
   *
   *
   *
   *                               The behavior of another party
   *
   *
   *
   *                               The manner in which an act is executed
   *
   *
   *
   *
   *                            Examples:A clinical protocol imposed by a payer to which a provider must adhere in order to be paid for providing the service.  A formulary from which a provider must select prescribed drugs in order for the patient to incur a lower copay.
   */
  BenefitPolicy: v3_ActCode_BenefitPolicy,
  /**
   * Codes representing the time period during which coverage is available; or financial participation requirements are in effect.
   */
  CoveragePeriod: v3_ActCode_CoveragePeriod,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedParkinsonsDiseaseCare: v3_ActCode_CertifiedParkinsonsDiseaseCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedPediatricsCare: v3_ActCode_CertifiedPediatricsCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedPhDMedicalGeneticsCare: v3_ActCode_CertifiedPhDMedicalGeneticsCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedPublicHealthAndGeneralPreventiveMedicineCare: v3_ActCode_CertifiedPublicHealthAndGeneralPreventiveMedicineCare,
  /**
   * Clinical product invoice where the Invoice Grouping contains one or more billable item and is supported by clinical product(s).
   *
   *                         For example, a crutch or a wheelchair.
   */
  ClinicalProductInvoice: v3_ActCode_ClinicalProductInvoice,
  /**
   * Custodian security system must retrieve, evaluate, and comply with the information handling directions of the Confidentiality Code associated with an information target.
   */
  ComplyWithConfidentialityCode: v3_ActCode_ComplyWithConfidentialityCode,
  /**
   * Custodian security system must retrieve, evaluate, and comply with applicable information subject consent directives.
   */
  ComplyWithConsentDirective: v3_ActCode_ComplyWithConsentDirective,
  /**
   * Custodian security system must retrieve, evaluate, and comply with applicable jurisdictional privacy policies associated with the target information.
   */
  ComplyWithJurisdictionalPrivacyPolicy: v3_ActCode_ComplyWithJurisdictionalPrivacyPolicy,
  /**
   * Custodian security system must retrieve, evaluate, and comply with applicable organizational privacy policies associated with the target information.
   */
  ComplyWithOrganizationalPrivacyPolicy: v3_ActCode_ComplyWithOrganizationalPrivacyPolicy,
  /**
   * Custodian security system must retrieve, evaluate, and comply with the organizational security policies associated with the target information.
   */
  ComplyWithOrganizationalSecurityPolicy: v3_ActCode_ComplyWithOrganizationalSecurityPolicy,
  /**
   * Custodian security system must retrieve, evaluate, and comply with applicable policies associated with the target information.
   */
  ComplyWithPolicy: v3_ActCode_ComplyWithPolicy,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedPneumoniaDiseaseCare: v3_ActCode_CertifiedPneumoniaDiseaseCare,
  /**
   * A grouping of invoice element groups and details including the ones specifying the compound ingredients being invoiced. It may also contain generic detail items such as markup.
   */
  CompoundDrugInvoiceGroup: v3_ActCode_CompoundDrugInvoiceGroup,
  /**
   * A grouping of invoice element details including the one specifying an ingredient drug being invoiced. It may also contain generic detail items such as tax or markup.
   */
  CompoundIngredientInvoiceGroup: v3_ActCode_CompoundIngredientInvoiceGroup,
  /**
   * A grouping of invoice element groups and details including the ones specifying the compound supplies being invoiced. It may also contain generic detail items such as markup.
   */
  CompoundSupplyInvoiceGroup: v3_ActCode_CompoundSupplyInvoiceGroup,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedPhysicalMedicineAndRehabilitationCare: v3_ActCode_CertifiedPhysicalMedicineAndRehabilitationCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedPlasticSurgeryCare: v3_ActCode_CertifiedPlasticSurgeryCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedPrimaryStrokeCenterCare: v3_ActCode_CertifiedPrimaryStrokeCenterCare,
  /**
   * Description:CPT modifier codes are found in Appendix A of CPT 2000 Standard Edition.
   */
  CPTModifierCodes: v3_ActCode_CPTModifierCodes,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedPsychiatryCare: v3_ActCode_CertifiedPsychiatryCare,
  /**
   * Description:Proposed therapy may be inappropriate or contraindicated because of a common but non-patient specific reaction to the product.
   *
   *
   *                            Example:There is no record of a specific sensitivity for the patient, but the presence of the sensitivity is common and therefore caution is warranted.
   */
  CommonReactionAlert: v3_ActCode_CommonReactionAlert,
  /**
   * Definition: A program that covers the cost of services provided to crime victims for injuries or losses related to the occurrence of a crime.
   */
  CrimeVictimProgram: v3_ActCode_CrimeVictimProgram,
  /**
   * A clinical judgment as to the worst case result of a future exposure (including substance administration). When the worst case result is assessed to have a life-threatening or organ system threatening potential, it is considered to be of high criticality.
   */
  Criticality: v3_ActCode_Criticality,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedRadiationOncologyCare: v3_ActCode_CertifiedRadiationOncologyCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedRadiologicalPhysicsCare: v3_ActCode_CertifiedRadiologicalPhysicsCare,
  /**
   * Summary of relevant clinical guidelines or other clinical recommendations supporting this eMeasure.
   */
  ClinicalRecommendationStatement: v3_ActCode_ClinicalRecommendationStatement,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedStrokeDiseaseManagementCare: v3_ActCode_CertifiedStrokeDiseaseManagementCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedSickleCellCare: v3_ActCode_CertifiedSickleCellCare,
  /**
   * Clinical Services Invoice which can be used to describe a single service, multiple services or repeated services.
   *
   *                         [1] Single Clinical services invoice where the Invoice Grouping contains one billable item and is supported by one clinical service.
   *
   *                         For example, a single service for an office visit or simple clinical procedure (e.g. knee mobilization).
   *
   *                         [2] Multiple Clinical services invoice where the Invoice Grouping contains more than one billable item, supported by one or more clinical services.  The services can be distinct and over multiple dates, but for the same patient. This type of invoice includes a series of treatments which must be adjudicated together.
   *
   *                         For example, an adjustment and ultrasound for a chiropractic session where fees are associated for each of the services and adjudicated (invoiced) together.
   *
   *                         [3] Repeated Clinical services invoice where the Invoice Grouping contains one or more billable item, supported by the same clinical service repeated over a period of time.
   *
   *                         For example, the same Chiropractic adjustment (service or treatment) delivered on 3 separate occasions over a period of time at the discretion of the provider (e.g. month).
   */
  ClinicalServiceInvoice: v3_ActCode_ClinicalServiceInvoice,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedSleepDisordersCare: v3_ActCode_CertifiedSleepDisordersCare,
  /**
   * A clinical Invoice Grouping consisting of one or more services and one or more product.  Billing for these service(s) and product(s) are supported by multiple clinical billable events (acts).
   *
   *                         All items in the Invoice Grouping must be adjudicated together to be acceptable to the Adjudicator.
   *
   *                         For example , a brace (product) invoiced together with the fitting (service).
   */
  ClinicalServiceAndProduct: v3_ActCode_ClinicalServiceAndProduct,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedSpineTreatmentCare: v3_ActCode_CertifiedSpineTreatmentCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedSurgeryCare: v3_ActCode_CertifiedSurgeryCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedTraumaBurnCenterCare: v3_ActCode_CertifiedTraumaBurnCenterCare,
  /**
   * Care coordination across participants in a care plan requires sharing of a healthcare consumer's information specific to that workflow.  A care team member should only have access to that information while participating in that workflow or for other authorized uses.
   *
   *                         Security Compartment Labels assigned to a consumer's information use in care coordination workflows should be met or exceeded by the Security Compartment attribute claimed by a participant in a care team member workflow who is requesting access to that information
   */
  CareTeamCompartment: v3_ActCode_CareTeamCompartment,
  /**
   * A monitoring program that focuses on narcotics and/or commonly abused substances that are subject to legal restriction.
   */
  ControlledSubstance: v3_ActCode_ControlledSubstance,
  /**
   * An observation that states the mechanism by which disease was acquired by the living subject involved in the public health case.
   *
   *
   *                            OpenIssue: This code could be moved to LOINC if it can be done before there are significant implemenations using it.
   */
  CaseTransmissionModeObservation: v3_ActCode_CaseTransmissionModeObservation,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedThoracicSurgeryCare: v3_ActCode_CertifiedThoracicSurgeryCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedUrologyCare: v3_ActCode_CertifiedUrologyCare,
  /**
   * List of current medications.
   */
  CurrentMedicationList: v3_ActCode_CurrentMedicationList,
  /**
   * The current quantity of the specimen, i.e., initial quantity minus what has been actually used.
   */
  CurrentVolume: v3_ActCode_CurrentVolume,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedVascularDiseasesCare: v3_ActCode_CertifiedVascularDiseasesCare,
  /**
   * Description:Scope of responsibility taken on for specialty care as defined by the respective Specialty Board.
   */
  CertifiedVascularSurgeryCare: v3_ActCode_CertifiedVascularSurgeryCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedWoundManagementCare: v3_ActCode_CertifiedWoundManagementCare,
  /**
   * Description:Scope of responsibility taken on by an organization for care of a patient as defined by the disease management certification agency.
   */
  CertifiedWomenQuoteSHealthCare: v3_ActCode_CertifiedWomenQuoteSHealthCare,
  /**
   * Description:Proposed therapy may be contraindicated or ineffective based on an existing or recent drug therapy.
   */
  DrugActionDetectedIssue: v3_ActCode_DrugActionDetectedIssue,
  /**
   * An allergy to a pharmaceutical product.
   */
  DrugAllergy: v3_ActCode_DrugAllergy,
  /**
   * Transaction counts and value totals for each calendar day within the date range specified.
   */
  Day: v3_ActCode_Day,
  /**
   * Electronic Funds Transfer (EFT) deposit into the payee's bank account
   */
  DirectDeposit: v3_ActCode_DirectDeposit,
  /**
   * Custodian security system must declassify information assigned security labels by instantiating a new version of the classified information so as to break the binding of the classifying security label when assigning a new security label that marks the information as unclassified in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the previous assignment and binding.
   */
  DeclassifySecurityLabel: v3_ActCode_DeclassifySecurityLabel,
  DEDUCT: v3_ActCode_DEDUCT,
  /**
   * That portion of the eligible charges which a covered party must pay in a particular period (e.g. annual) before the benefits are payable by the adjudicator. This amount represents the covered party's deductible that is applied to a particular adjudication result. It is expressed as a negative dollar amount in adjudication results.
   */
  Deductible: v3_ActCode_Deductible,
  /**
   * Description of individual terms, provided as needed.
   */
  Definition: v3_ActCode_Definition,
  /**
   * The removal of fibrin from whole blood or plasma through physical or chemical means
   */
  Defibrination: v3_ActCode_Defibrination,
  /**
   * Custodian system must strip information of data that would allow the identification of the source of the information or the information subject.
   */
  Deidentify: v3_ActCode_Deidentify,
  /**
   * Custodian system must remove target information from access after use.
   */
  DeleteAfterUse: v3_ActCode_DeleteAfterUse,
  /**
   * Delegation policies specify which actions subjects are allowed to delegate to others. A delegation policy thus specifies an authorisation to delegate. Subjects must already possess the access rights to be delegated.
   *
   *                         Delegation policies are aimed at subjects delegating rights to servers or third parties to perform actions on their behalf and are not meant to be the means by which security administrators would assign rights to subjects. A negative delegation policy identifies what delegations are forbidden.
   *
   *                         A Delegation policy specifies the authorisation policy from which delegated rights are derived, the grantors, which are the entities which can delegate these access rights, and the grantees, which are the entities to which the access rights can be delegated. There are two types of delegation policy, positive and negative. (Based on PONDERS)
   */
  DelegationPolicy: v3_ActCode_DelegationPolicy,
  /**
   * Policy for handling all demographic information about an information subject, which will be afforded heightened confidentiality. Policies may govern sensitivity of information related to all demographic about an information subject, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  AllDemographicInformationSensitivity: v3_ActCode_AllDemographicInformationSensitivity,
  /**
   * Definition:All information pertaining to a patient's demographics (such as name, date of birth, gender, address, etc).
   */
  DemographicsCategory: v3_ActCode_DemographicsCategory,
  /**
   * Criteria which specify subjects who should be removed from the eMeasure population and denominator before determining if numerator criteria are met. Denominator exclusions are used in proportion and ratio measures to help narrow the denominator.
   */
  DenominatorExclusions: v3_ActCode_DenominatorExclusions,
  /**
   * Criteria which specify the removal of a subject, procedure or unit of measurement from the denominator, only if the numerator criteria are not met. Denominator exceptions allow for adjustment of the calculated score for those providers with higher risk populations. Denominator exceptions are used only in proportion eMeasures. They are not appropriate for ratio or continuous variable eMeasures. Denominator exceptions allow for the exercise of clinical judgment and should be specifically defined where capturing the information in a structured manner fits the clinical workflow. Generic denominator exception reasons used in proportion eMeasures fall into three general categories:
   *
   *
   *                            Medical reasons
   *                            Patient (or subject) reasons
   *                            System reasons
   */
  DenominatorExceptions: v3_ActCode_DenominatorExceptions,
  /**
   * Criteria for specifying the entities to be evaluated by a specific quality measure, based on a shared common set of characteristics (within a specific measurement set to which a given measure belongs).  The denominator can be the same as the initial population, or it may be a subset of the initial population to further constrain it for the purpose of the eMeasure. Different measures within an eMeasure set may have different denominators. Continuous Variable eMeasures do not have a denominator, but instead define a measure population.
   */
  Denominator: v3_ActCode_Denominator,
  /**
   * Definition: A health insurance policy that that covers benefits for dental services.
   */
  DentalCarePolicy: v3_ActCode_DentalCarePolicy,
  /**
   * Definition: A public or government health program that administers and funds coverage for dental care to assist program eligible who meet financial and health status criteria.
   */
  DentalProgram: v3_ActCode_DentalProgram,
  /**
   * Communication of an agent from a living subject or environmental source to a living subject via agent migration through intact skin.
   */
  TransdermalTransmission: v3_ActCode_TransdermalTransmission,
  /**
   * A fill providing sufficient supply for one day
   */
  DailyFill: v3_ActCode_DailyFill,
  /**
   * Policy for handling information related to a diagnosis, health condition or health problem, which will be afforded heightened confidentiality.  Diagnostic, health condition or health problem related information may be deemed sensitive by organizational policy, and require heightened confidentiality.
   *
   *
   *                            Usage Note: For use within an enterprise that provides heightened confidentiality to  diagnostic, health condition or health problem related information deemed sensitive.   If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  DiagnosisInformationSensitivity: v3_ActCode_DiagnosisInformationSensitivity,
  /**
   * A clinician enters a diagnosis for a given patient.
   */
  DiagnosisListEntryTask: v3_ActCode_DiagnosisListEntryTask,
  /**
   * A person reviews a list of diagnoses of a given patient.
   */
  DiagnosisListReviewTask: v3_ActCode_DiagnosisListReviewTask,
  /**
   * Definition:All information pertaining to a patient's diagnostic image records (orders & results).
   */
  DiagnosticImageCategory: v3_ActCode_DiagnosticImageCategory,
  /**
   * Code set to define specialized/allowed diets
   */
  Diet: v3_ActCode_Diet,
  /**
   * An observation that reports the dilution of a sample.
   */
  ActSpecObsDilutionCode: v3_ActCode_ActSpecObsDilutionCode,
  /**
   * Hypersensitivity resulting in an adverse reaction upon exposure to a drug.
   */
  DrugIntolerance: v3_ActCode_DrugIntolerance,
  /**
   * Definition: An insurance policy that provides a regular payment to compensate for income lost due to the covered party's inability to work because of illness or injury.
   */
  DisabilityInsurancePolicy: v3_ActCode_DisabilityInsurancePolicy,
  /**
   * Disclaimer information for the eMeasure.
   */
  Disclaimer: v3_ActCode_Disclaimer,
  /**
   * A person provides a discharge instruction to a patient.
   */
  DischargeInstructionEntry: v3_ActCode_DischargeInstructionEntry,
  /**
   * A clinician enters a discharge summary for a given patient.
   */
  DischargeSummaryEntryTask: v3_ActCode_DischargeSummaryEntryTask,
  /**
   * A person reviews a discharge summary of a given patient.
   */
  DischargeSummaryReviewTask: v3_ActCode_DischargeSummaryReviewTask,
  /**
   * List of discharge medications.
   */
  DischargeMedicationList: v3_ActCode_DischargeMedicationList,
  /**
   * Discharge diagnosis are the diagnoses documented for administrative purposes as the time of hospital discharge.
   */
  DischargeDiagnosis: v3_ActCode_DischargeDiagnosis,
  /**
   * Definition: A health insurance policy that covers benefits for healthcare services provided for named conditions under the policy, e.g., cancer, diabetes, or HIV-AIDS.
   */
  DiseaseSpecificPolicy: v3_ActCode_DiseaseSpecificPolicy,
  /**
   * Definition: A public or government health program that administers and funds coverage for health and social services to assist program eligible who meet financial and health status criteria related to a particular disease.
   *
   *
   *                            Example: Reproductive health, sexually transmitted disease, and end renal disease programs.
   */
  PublicHealthProgram: v3_ActCode_PublicHealthProgram,
  /**
   * The adjudication result associated is to be displayed to the receiver of the adjudication result.
   */
  Display: v3_ActCode_Display,
  /**
   * A diet that uses carbohydrates sparingly.  Typically with a restriction in daily energy content (e.g. 1600-2000 kcal).
   */
  DiabetesMellitusDiet: v3_ActCode_DiabetesMellitusDiet,
  /**
   * Diner's Club
   */
  DinerQuoteSClub: v3_ActCode_DinerQuoteSClub,
  /**
   * Hypersensitivity to an agent caused by a mechanism other than an immunologic response to an initial exposure
   */
  DrugNonAllergyIntolerance: v3_ActCode_DrugNonAllergyIntolerance,
  /**
   * Provision of treatment for oral health and/or dental surgery.
   */
  Dental: v3_ActCode_Dental,
  /**
   * Policy for handling information related to an information subject's date of birth, which will be afforded heightened confidentiality.Policies may govern sensitivity of information related to an information subject's date of birth, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  DateOfBirthInformationSensitivity: v3_ActCode_DateOfBirthInformationSensitivity,
  /**
   * Description: Document Attachment
   */
  Document: v3_ActCode_Document,
  /**
   * Proposed dosage instructions for therapy differ from standard practice.
   */
  DosageProblem: v3_ActCode_DosageProblem,
  /**
   * Description:Proposed dosage is inappropriate due to patient's medical condition.
   */
  DosageConditionAlert: v3_ActCode_DosageConditionAlert,
  /**
   * Proposed length of therapy differs from standard practice.
   */
  DoseDurationAlert: v3_ActCode_DoseDurationAlert,
  /**
   * Proposed length of therapy is longer than standard practice
   */
  DoseDurationHighAlert: v3_ActCode_DoseDurationHighAlert,
  /**
   * Proposed length of therapy is longer than standard practice for the identified indication or diagnosis
   */
  DoseDurationHighForIndicationAlert: v3_ActCode_DoseDurationHighForIndicationAlert,
  /**
   * Proposed length of therapy is shorter than that necessary for therapeutic effect
   */
  DoseDurationLowAlert: v3_ActCode_DoseDurationLowAlert,
  /**
   * Proposed length of therapy is shorter than standard practice for the identified indication or diagnosis
   */
  DoseDurationLowForIndicationAlert: v3_ActCode_DoseDurationLowForIndicationAlert,
  /**
   * Proposed dosage exceeds standard practice
   */
  HighDoseAlert: v3_ActCode_HighDoseAlert,
  /**
   * High Dose for Indication Alert
   */
  HighDoseForIndicationAlert: v3_ActCode_HighDoseForIndicationAlert,
  /**
   * Proposed dosage exceeds standard practice for the patient's age
   */
  HighDoseForAgeAlert: v3_ActCode_HighDoseForAgeAlert,
  /**
   * Proposed dosage exceeds standard practice for the patient's height or body surface area
   */
  HighDoseForHeightSurfaceAreaAlert: v3_ActCode_HighDoseForHeightSurfaceAreaAlert,
  /**
   * Proposed dosage exceeds standard practice for the patient's weight
   */
  HighDoseForWeightAlert: v3_ActCode_HighDoseForWeightAlert,
  DOSEIND: v3_ActCode_DOSEIND,
  /**
   * Proposed dosage interval/timing differs from standard practice
   */
  DoseIntervalAlert: v3_ActCode_DoseIntervalAlert,
  /**
   * Proposed dosage interval/timing differs from standard practice for the identified indication or diagnosis
   */
  DoseIntervalForIndicationAlert: v3_ActCode_DoseIntervalForIndicationAlert,
  /**
   * Proposed dosage is below suggested therapeutic levels
   */
  LowDoseAlert: v3_ActCode_LowDoseAlert,
  /**
   * Low Dose for Indication Alert
   */
  LowDoseForIndicationAlert: v3_ActCode_LowDoseForIndicationAlert,
  /**
   * Proposed dosage is below suggested therapeutic levels for the patient's age
   */
  LowDoseForAgeAlert: v3_ActCode_LowDoseForAgeAlert,
  /**
   * Proposed dosage is below suggested therapeutic levels for the patient's height or body surface area
   */
  LowDoseForHeightSurfaceAreaAlert: v3_ActCode_LowDoseForHeightSurfaceAreaAlert,
  /**
   * Proposed dosage is below suggested therapeutic levels for the patient's weight
   */
  LowDoseForWeightAlert: v3_ActCode_LowDoseForWeightAlert,
  /**
   * Custodian security system must downgrade information assigned security labels by instantiating a new version of the classified information so as to break the binding of the classifying security label when assigning a new security label that marks the information as classified at a less protected level in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the previous assignment and binding.
   */
  DowngradeSecurityLabel: v3_ActCode_DowngradeSecurityLabel,
  /**
   * Proposed therapy may interact with an existing or recent drug therapy
   */
  DrugInteractionAlert: v3_ActCode_DrugInteractionAlert,
  /**
   * Policy for handling information related to a drug, which will be afforded heightened confidentiality. Drug information may be deemed sensitive by organizational policy, and require heightened confidentiality.
   *
   *
   *                            Usage Note: For use within an enterprise that provides heightened confidentiality to drug information deemed sensitive.   If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  DrugInformationSensitivity: v3_ActCode_DrugInformationSensitivity,
  /**
   * Provision of treatment for drug abuse.
   */
  DrugRehab: v3_ActCode_DrugRehab,
  /**
   * Custodian security system must assign and bind security labels derived from compilations of information by aggregation or disaggregation in order to classify information compiled in the information systems under its control for collection, access, use and disclosure in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the previous assignment and binding.
   */
  DeriveSecurityLabel: v3_ActCode_DeriveSecurityLabel,
  /**
   * The introduction of a drug into a subject with the intention of altering its biologic state with the intent of improving its health status.
   */
  DrugTherapy: v3_ActCode_DrugTherapy,
  /**
   * A grouping of invoice element details including the one specifying the drug being invoiced. It may also contain generic detail items such as markup.
   */
  DrugInvoiceGroup: v3_ActCode_DrugInvoiceGroup,
  /**
   * Definition: A health insurance policy that covers benefits for prescription drugs, pharmaceuticals, and supplies.
   */
  DrugPolicy: v3_ActCode_DrugPolicy,
  /**
   * Definition: A public or government health program that administers and funds coverage for prescription drugs to assist program eligible who meet financial and health status criteria.
   */
  DrugProgram: v3_ActCode_DrugProgram,
  /**
   * A reduction in the amount charged as a percentage of the amount. For example a 5% discount for volume purchase.
   */
  Discount: v3_ActCode_Discount,
  /**
   * Description:The proposed therapy appears to have the same intended therapeutic benefit as an existing therapy, though the specific mechanisms of action vary.
   */
  DuplicateTherapeuticAlassAlert: v3_ActCode_DuplicateTherapeuticAlassAlert,
  /**
   * Description:The proposed therapy appears to have the same intended therapeutic benefit as an existing therapy and uses the same mechanisms of action as the existing therapy.
   */
  DuplicateGenericAlert: v3_ActCode_DuplicateGenericAlert,
  /**
   * The proposed therapy appears to duplicate an existing therapy
   */
  DuplicateTherapyAlert: v3_ActCode_DuplicateTherapyAlert,
  /**
   * Discover Card
   */
  DiscoverCard: v3_ActCode_DiscoverCard,
  /**
   * Policy for handling information related to developmental disability disorders and conditions caused by these disorders, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   *
   *                         A diverse group of chronic conditions that are due to mental or physical impairments impacting activities of daily living, self-care, language acuity, learning, mobility, independent living and economic self-sufficiency. Examples may include Down syndrome and  Autism spectrum. However, the developmental disabilities to which this term applies versus other behavioral health categories varies by jurisdiction and organizational policy in part due to overlap with other behavioral health conditions.  Implementers should constrain to those diagnoses applicable in the domain in which this code is used.
   */
  DevelopmentalDisabilityInformationSensitivity: v3_ActCode_DevelopmentalDisabilityInformationSensitivity,
  /**
   * Includes all codes defining types of indications such as diagnosis, symptom and other indications such as contrast agents for lab tests.
   */
  ObservationDiagnosisTypes: v3_ActCode_ObservationDiagnosisTypes,
  /**
   * An allergy to a substance other than a drug or a food.  E.g. Latex, pollen, etc.
   */
  EnvironmentalAllergy: v3_ActCode_EnvironmentalAllergy,
  /**
   * Definition: An employee assistance program is run by an employer or employee organization for the purpose of providing benefits and covering all or part of the cost for employees to receive counseling, referrals, and advice in dealing with stressful issues in their lives. These may include substance abuse, bereavement, marital problems, weight issues, or general wellness issues.  The services are usually provided by a third-party, rather than the company itself, and the company receives only summary statistical data from the service provider. Employee's names and services received are kept confidential.
   */
  EmployeeAssistanceProgram: v3_ActCode_EmployeeAssistanceProgram,
  /**
   * Fees deducted on behalf of a payee for tuition and continuing education.
   */
  EducationFees: v3_ActCode_EducationFees,
  /**
   * Electronic form with supporting or additional information to follow.
   */
  ElectronicFormToFollow: v3_ActCode_ElectronicFormToFollow,
  /**
   * Private insurance policy that provides coverage in addition to other policies (e.g. in addition to a Public Healthcare insurance policy).
   */
  ExtendedHealthcare: v3_ActCode_ExtendedHealthcare,
  /**
   * Hypersensitivity resulting in an adverse reaction upon exposure to environmental conditions.
   */
  EnvironmentalIntolerance: v3_ActCode_EnvironmentalIntolerance,
  /**
   * Insurance coverage is in effect for healthcare service(s) and/or product(s).
   */
  Eligible: v3_ActCode_Eligible,
  /**
   * An ellipse defined by four (column,row) pairs, the first two points specifying the endpoints of the major axis and the second two points specifying the endpoints of the minor axis.
   */
  Ellipse: v3_ActCode_Ellipse,
  /**
   * A supply action where there is no 'valid' order for the supplied medication.  E.g. Emergency vacation supply, weekend supply (when prescriber is unavailable to provide a renewal prescription)
   */
  EmergencySupply: v3_ActCode_EmergencySupply,
  /**
   * Used to temporarily override normal authorization rules to gain access to data in a case of emergency. Use of this override code will typically be monitored, and a procedure to verify its proper use may be triggered when used.
   */
  EmergencyAuthorizationOverride: v3_ActCode_EmergencyAuthorizationOverride,
  /**
   * A patient encounter that takes place at a dedicated healthcare service delivery location where the patient receives immediate evaluation and treatment, provided until the patient can be discharged or responsibility for the patient's care is transferred elsewhere (for example, the patient could be admitted as an inpatient or transferred to another facility.)
   */
  Emergency: v3_ActCode_Emergency,
  /**
   * Policy for handling information related to emotional disturbance disorders and conditions caused by these disorders, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   *
   *                         Typical used to characterize behavioral and mental health issues of adolescents where the disorder may be temporarily diagnosed in order to avoid the potential and unnecessary stigmatizing diagnoses of disorder long term.
   */
  EmotionalDisturbanceInformationSensitivity: v3_ActCode_EmotionalDisturbanceInformationSensitivity,
  /**
   * Policy for handling information related to an employee, which will be afforded heightened confidentiality. When a patient is an employee, an enterprise may have a policy that requires heightened confidentiality.  Information deemed sensitive typically includes health information and patient role information including patient status, demographics, next of kin, and location.
   *
   *
   *                            Usage Note: Policy for handling information related to an employee, which will be afforded heightened confidentiality.  Description:  When a patient is an employee, an enterprise may have a policy that requires heightened confidentiality.  Information deemed sensitive typically includes health information and patient role information including patient status, demographics, next of kin, and location.
   */
  EmployeeInformationSensitivity: v3_ActCode_EmployeeInformationSensitivity,
  /**
   * Policy for handling information related to an employer which is deemed classified to protect an employee who is the information subject, and which will be afforded heightened confidentiality.  Description:  Policies may govern sensitivity of information related to an employer, such as law enforcement or national security, the identity of which could impact the privacy, well-being, or safety of an information subject who is an employee.
   *
   *
   *                            Usage Notes: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  EmployerInformationSensitivity: v3_ActCode_EmployerInformationSensitivity,
  /**
   * This general consent directive specifically limits disclosure of health information for purpose of emergency treatment. Additional parameters may further limit the disclosure to specific users, roles, duration, types of information, and impose uses obligations.
   *
   *
   *                            Definition: Opt-in to disclosure of health information for emergency only consent directive.
   */
  EmergencyOnly: v3_ActCode_EmergencyOnly,
  /**
   * Hypersensitivity to an agent caused by a mechanism other than an immunologic response to an initial exposure
   */
  EnvironmentalNonAllergyIntolerance: v3_ActCode_EnvironmentalNonAllergyIntolerance,
  /**
   * Custodian system must render information unreadable by algorithmically transforming plaintext into ciphertext.
   *
   *
   *
   *
   *                            Usage Notes: A mathematical transposition of a file or data stream so that it cannot be deciphered at the receiving end without the proper key. Encryption is a security feature that assures that only the parties who are supposed to be participating in a videoconference or data transfer are able to do so. It can include a password, public and private keys, or a complex combination of all.  (Per Infoway.)
   */
  Encrypt: v3_ActCode_Encrypt,
  /**
   * Custodian system must render information unreadable and unusable by algorithmically transforming plaintext into ciphertext when "at rest" or in storage.
   */
  EncryptAtRest: v3_ActCode_EncryptAtRest,
  /**
   * Custodian system must render information unreadable and unusable by algorithmically transforming plaintext into ciphertext while "in transit" or being transported by any means.
   */
  EncryptInTransit: v3_ActCode_EncryptInTransit,
  /**
   * Custodian system must render information unreadable and unusable by algorithmically transforming plaintext into ciphertext while in use such that operations permitted on the target information are limited by the license granted to the end user.
   */
  EncryptInUse: v3_ActCode_EncryptInUse,
  /**
   * A baseline value for the measured test that is inherently contained in the diluent.  In the calculation of the actual result for the measured test, this baseline value is normally considered.
   */
  EndogenousContent: v3_ActCode_EndogenousContent,
  /**
   * Proposed therapy may be inappropriate or ineffective because the end of administration is too close to another planned therapy
   */
  EndTooLateAlert_2: v3_ActCode_EndTooLateAlert_2,
  /**
   * Definition: A public or government program that administers publicly funded coverage of kidney dialysis and kidney transplant services.
   *
   *                         Example: In the U.S., the Medicare End-stage Renal Disease program (ESRD), the National Kidney Foundation (NKF) American Kidney Fund (AKF) The Organ Transplant Fund.
   */
  EndRenalProgram: v3_ActCode_EndRenalProgram,
  /**
   * Communication of an agent from an environmental surface or source to a living subject by direct contact.
   */
  EnvironmentalExposureTransmission: v3_ActCode_EnvironmentalExposureTransmission,
  /**
   * Fees deducted on behalf of a payee for charges based on a shorter payment frequency (i.e. next day versus biweekly payments.
   */
  EarlyPaymentFee: v3_ActCode_EarlyPaymentFee,
  /**
   * A premium on a service fee is requested because, due to extenuating circumstances, the service took an extraordinary amount of time or supplies.
   */
  ExtraordinaryServiceAssessment: v3_ActCode_ExtraordinaryServiceAssessment,
  /**
   * Policy for handling alcohol or drug-abuse information, which will be afforded heightened confidentiality.  Information handling protocols based on organizational policies related to alcohol or drug-abuse information that is deemed sensitive.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  SubstanceAbuseInformationSensitivity: v3_ActCode_SubstanceAbuseInformationSensitivity,
  /**
   * Policy for handling information related to alcohol use disorders and conditions caused by these disorders, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  AlcoholUseDisorderInformationSensitivity: v3_ActCode_AlcoholUseDisorderInformationSensitivity,
  /**
   * Domain provides codes that qualify the ActLabObsEnvfctsCode domain. (Environmental Factors)
   */
  ActSpecObsEvntfctsCode: v3_ActCode_ActSpecObsEvntfctsCode,
  /**
   * Definition: An insurance policy under a benefit plan run by an employer or employee organization for the purpose of providing benefits other than pension-related to employees and their families. Typically provides health-related benefits, benefits for disability, disease or unemployment, or day care and scholarship benefits, among others.  An employer sponsored health policy includes coverage of health care expenses arising from sickness or accidental injury, coverage for on-site medical clinics or for dental or vision benefits, which are typically provided under a separate policy.  Coverage excludes health care expenses covered by accident or disability, workers' compensation, liability or automobile insurance.
   */
  EmployeeWelfareBenefitPlanPolicy: v3_ActCode_EmployeeWelfareBenefitPlanPolicy,
  /**
   * Description:Indicates that a result is complete.  No further results are to come.  This maps to the 'complete' state in the observation result status code.
   */
  Final: v3_ActCode_Final,
  /**
   * An allergy to a substance generally consumed for nutritional purposes.
   */
  FoodAllergy: v3_ActCode_FoodAllergy,
  /**
   * A person reviews a Falls Risk Assessment Instrument report of a given patient.
   */
  FallsRiskAssessmentInstrumentTask: v3_ActCode_FallsRiskAssessmentInstrumentTask,
  /**
   * No enteral intake of foot or liquids  whatsoever, no smoking.  Typically 6 to 8 hours before anesthesia.
   */
  Fasting: v3_ActCode_Fasting,
  /**
   * Fax with supporting or additional information to follow.
   */
  FaxToFollow: v3_ActCode_FaxToFollow,
  /**
   * Description: The introduction of material into a subject with the intent of providing nutrition or other dietary supplements (e.g. minerals or vitamins).
   */
  Food: v3_ActCode_Food,
  /**
   * FDA label coating
   */
  Coating: v3_ActCode_Coating,
  /**
   * FDA label color
   */
  Color: v3_ActCode_Color,
  /**
   * FDA label imprint code
   */
  ImprintCode: v3_ActCode_ImprintCode,
  /**
   * FDA label logo
   */
  Logo: v3_ActCode_Logo,
  /**
   * FDA label scoring
   */
  Scoring: v3_ActCode_Scoring,
  /**
   * FDA label shape
   */
  Shape: v3_ActCode_Shape,
  /**
   * FDA label size
   */
  Size: v3_ActCode_Size,
  /**
   * Communication of an agent from a living subject or environmental source to a living subject through oral contact with material contaminated by person or animal fecal material.
   */
  FecalOralTransmission: v3_ActCode_FecalOralTransmission,
  /**
   * The initial fill against an order.  (This includes initial fills against refill orders.)
   */
  FirstFill: v3_ActCode_FirstFill,
  /**
   * A first fill where the quantity supplied is equal to one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a complete fill would be for the full 90 tablets).
   */
  FirstFillComplete: v3_ActCode_FirstFillComplete,
  /**
   * A first fill where the quantity supplied is equal to one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a complete fill would be for the full 90 tablets) and also where the strength supplied is less than the ordered strength (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  FirstFillCompletePartialStrength: v3_ActCode_FirstFillCompletePartialStrength,
  /**
   * A first fill where the quantity supplied is less than one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a partial fill might be for only 30 tablets.)
   */
  FirstFillPartFill: v3_ActCode_FirstFillPartFill,
  /**
   * A first fill where the quantity supplied is less than one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a partial fill might be for only 30 tablets.) and also where the strength supplied is less than the ordered strength (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets)
   */
  FirstFillPartFillPartialStrength: v3_ActCode_FirstFillPartFillPartialStrength,
  /**
   * A billing arrangement where a Provider charges a separate fee for each intervention/procedure/event or product.
   *
   *                         Fee for Service is used when an individual intervention/procedure/event is used for billing purposes.  In other words, fees are associated with the  intervention/procedure/event.  For example, a specific CCI (Canadian Classification of Interventions) code has an associated fee and is used for billing purposes.
   */
  FeeForService: v3_ActCode_FeeForService,
  /**
   * A first fill where the strength supplied is less than the ordered strength. (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  FirstFillPartialStrength: v3_ActCode_FirstFillPartialStrength,
  /**
   * Under agreement between the parties (payor and provider), a guaranteed level of income is established for the provider over a specific, pre-determined period of time. The normal course of business for the provider is submission of fee-for-service claims. Should the fee-for-service income during the specified period of time be less than the agreed to amount, a top-up amount is paid to the provider equal to the difference between the fee-for-service total and the guaranteed income amount for that period of time. The details of the agreement may specify (or not) a requirement for repayment to the payor in the event that the fee-for-service income exceeds the guaranteed amount.
   */
  FeeForServiceTopOff: v3_ActCode_FeeForServiceTopOff,
  /**
   * The Fibrin Index of the specimen. In the case of only differentiating between Absent and Present, recommend using 0 and 1
   */
  Fibrin: v3_ActCode_Fibrin,
  /**
   * The passage of a liquid through a filter, accomplished by gravity, pressure or vacuum (suction).
   */
  Filtration: v3_ActCode_Filtration,
  /**
   * The timestamp when the eMeasure was last packaged in the Measure Authoring Tool.
   */
  FinalizedDateTime: v3_ActCode_FinalizedDateTime,
  /**
   * A billing arrangement where a Provider charges for non-clinical items.  This includes interest in arrears, mileage, etc.  Clinical content is not 	included in Invoices submitted with this type of billing arrangement.
   */
  Financial: v3_ActCode_Financial,
  /**
   * Invoice Grouping without clinical justification.  These will not require identification of participants and associations from a clinical context such as patient and provider.
   *
   *                         Examples are interest charges and mileage.
   */
  FinancialInvoice: v3_ActCode_FinancialInvoice,
  /**
   * Hypersensitivity resulting in an adverse reaction upon exposure to food.
   */
  FoodIntolerance: v3_ActCode_FoodIntolerance,
  /**
   * A patient encounter that takes place both outside a dedicated service delivery location and outside a patient's residence. Example locations might include an accident site and at a supermarket.
   */
  Field: v3_ActCode_Field,
  /**
   * Definition:  An insurance policy that covers qualified benefits under a Flexible Benefit plan such as group medical insurance, long and short term disability income insurance, group term life insurance for employees only up to $50,000 face amount, specified disease coverage such as a cancer policy, dental and/or vision insurance, hospital indemnity insurance, accidental death and dismemberment insurance, a medical expense reimbursement plan and a dependent care reimbursement plan.
   *
   *
   *                             Discussion: See UnderwriterRoleTypeCode flexible benefit plan which is defined as a benefit plan that allows employees to choose from several life, health, disability, dental, and other insurance plans according to their individual needs. Also known as cafeteria plans.  Authorized under Section 125 of the Revenue Act of 1978.
   */
  FlexibleBenefitPlanPolicy: v3_ActCode_FlexibleBenefitPlanPolicy,
  /**
   * Financial management department members who have access to healthcare consumer information as part of a patient account, billing and claims workflows.
   *
   *                         Security Compartment Labels assigned to consumer information used in these workflows should be met or exceeded by the Security Compartment attribute claimed by a participant in a financial management workflow who is requesting access to that information.
   */
  FinancialManagementCompartment: v3_ActCode_FinancialManagementCompartment,
  /**
   * Hypersensitivity to an agent caused by a mechanism other than an immunologic response to an initial exposure
   */
  FoodNonAllergyIntolerance: v3_ActCode_FoodNonAllergyIntolerance,
  /**
   * Anticipated or actual final fee associated with treating a patient.
   */
  FinalFee: v3_ActCode_FinalFee,
  /**
   * Communication of an agent from an non-living material to a living subject through direct contact.
   */
  FomiteTransmission: v3_ActCode_FomiteTransmission,
  /**
   * Proposed therapy may interact with certain foods
   */
  FoodInteractionAlert: v3_ActCode_FoodInteractionAlert,
  /**
   * Communication of an agent from a food source to a living subject via oral consumption.
   */
  FoodBorneTransmission: v3_ActCode_FoodBorneTransmission,
  /**
   * The adjudication result associated is to be printed on the specified form, which is then provided to the covered party.
   */
  PrintOnForm: v3_ActCode_PrintOnForm,
  /**
   * Description:The element does not follow the formatting or type rules defined for the field.
   */
  InvalidFormat: v3_ActCode_InvalidFormat,
  /**
   * A diet consisting of a formula feeding, either for an infant or an adult, to provide nutrition either orally or through the gastrointestinal tract via tube, catheter or stoma.
   */
  FormulaDiet: v3_ActCode_FormulaDiet,
  /**
   * A grouping of invoice element details including the ones specifying the frame fee and the frame dispensing cost that are being invoiced.
   */
  FrameInvoiceGroup: v3_ActCode_FrameInvoiceGroup,
  /**
   * Description:The request is suspected to have a fraudulent basis.
   */
  PotentialFraud: v3_ActCode_PotentialFraud,
  /**
   * Anticipated or actual initial fee associated with treating a patient.
   */
  FirstFee: v3_ActCode_FirstFee,
  /**
   * A supply action to restock a smaller more local dispensary.
   */
  FloorStock: v3_ActCode_FloorStock,
  /**
   * Federal tax on transactions such as the Goods and Services Tax (GST)
   */
  FederalSalesTax: v3_ActCode_FederalSalesTax,
  /**
   * Definition:The therapy being performed is in some way out of alignment with the requested therapy.
   */
  FulfillmentAlert: v3_ActCode_FulfillmentAlert,
  /**
   * Fees deducted on behalf of a payee for charges based on a per-transaction or time-period (e.g. monthly) fee.
   */
  Garnishee: v3_ActCode_Garnishee,
  /**
   * Policy for handling genetic disease information, which will be afforded heightened confidentiality. Information handling protocols based on organizational policies related to genetic disease information that is deemed sensitive.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  GeneticDiseaseInformationSensitivity: v3_ActCode_GeneticDiseaseInformationSensitivity,
  /**
   * Proposed therapy is outside of standard practice for a geriatric patient.
   */
  GeriatricAlert: v3_ActCode_GeriatricAlert,
  /**
   * Proposed therapy may be inappropriate or contraindicated due to patient genetic indicators.
   */
  GeneticAlert: v3_ActCode_GeneticAlert,
  /**
   * Proposed therapy may be inappropriate or contraindicated due to patient gender.
   */
  GenderAlert: v3_ActCode_GenderAlert,
  /**
   * Policy for handling information related to an information subject's gender and sexual orientation, which will be afforded heightened confidentiality.  Policies may govern sensitivity of information related to an information subject's gender and sexual orientation, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  GenderAndSexualOrientationInformationSensitivity: v3_ActCode_GenderAndSexualOrientationInformationSensitivity,
  /**
   * Description: A DNA segment that contributes to phenotype/function. In the absence of demonstrated function a gene may be characterized by sequence, transcription or homology
   */
  Gene: v3_ActCode_Gene,
  /**
   * General care performed by a general practitioner or family doctor as a responsible provider for a patient.
   */
  General: v3_ActCode_General,
  /**
   * Gluten free diet for celiac disease.
   */
  GlutenFree: v3_ActCode_GlutenFree,
  /**
   * The medical service was provided to a patient in good faith that they had medical coverage, although no evidence of coverage was available before service was rendered.
   */
  GoodFaithIndicator: v3_ActCode_GoodFaithIndicator,
  /**
   * Description: Accuracy determined as per the GIS tier code system.
   */
  GISTier: v3_ActCode_GISTier,
  /**
   * List of observations in goal mood.
   */
  GoalList: v3_ActCode_GoalList,
  /**
   * Definition: A set of codes used to indicate a government program that is an organized structure for administering and funding coverage of a benefit package for covered parties meeting eligibility criteria, typically related to employment, health and financial status. Government programs are established or permitted by legislation with provisions for ongoing government oversight.  Regulation mandates the structure of the program, the manner in which it is funded and administered, covered benefits, provider types, eligibility criteria and financial participation. A government agency is charged with implementing the program in accordance to the regulation
   *
   *
   *                            Example: Federal employee health benefit program in the U.S.
   */
  GovernmentEmployeeHealthProgram: v3_ActCode_GovernmentEmployeeHealthProgram,
  /**
   * Description: The school grade or level the patient was in when immunized.
   */
  Grade: v3_ActCode_Grade,
  /**
   * A grantor's terms of agreement to which a grantee may assent or dissent, and which may include an opportunity for a grantee to request restrictions or extensions.
   *
   *
   *                            Comment: A grantor typically is able to stipulate preferred terms of agreement when the grantor has control over the topic of the agreement, which a grantee must accept in full or may be offered an opportunity to extend or restrict certain terms.
   *
   *
   *                            Usage Note: If the grantor's term of agreement must be accepted in full, then this is considered "basic consent".  If a grantee is offered an opportunity to extend or restrict certain terms, then the agreement is considered "granular consent".
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare: A PHR account holder [grantor] may require any PHR user [grantee]  to accept the terms of agreement in full, or may permit a PHR user to extend or restrict terms selected by the account holder or requested by the PHR user.
   *                            Non-healthcare: The owner of a resource server [grantor] may require any authorization server [grantee] to meet authorization requirements stipulated in the grantor's terms of agreement.
   */
  GrantorChoice: v3_ActCode_GrantorChoice,
  /**
   * Description:Global Trade Item Number is an identifier for trade items developed by GS1 (comprising the former EAN International and Uniform Code Council).
   */
  GlobalTradeItemNumber: v3_ActCode_GlobalTradeItemNumber,
  /**
   * Used to allow measure developers to provide additional guidance for implementers to understand greater specificity than could be provided in the logic for data criteria.
   */
  Guidance: v3_ActCode_Guidance,
  /**
   * Description:HCPCS Level II (HCFA-assigned) and Carrier-assigned (Level III) modifiers are reported in Appendix A of CPT 2000 Standard Edition and in the Medicare Bulletin.
   */
  HCPCSLevelIIAndCarrierAssigned: v3_ActCode_HCPCSLevelIIAndCarrierAssigned,
  /**
   * Description: Health Record Attachment
   */
  HealthRecord: v3_ActCode_HealthRecord,
  /**
   * Definition:There should be no actions taken in fulfillment of a request that has been held or suspended.
   */
  HeldSuspendedAlert: v3_ActCode_HeldSuspendedAlert,
  /**
   * An observation of the hemolysis index of the specimen in g/L
   */
  Hemolysis: v3_ActCode_Hemolysis,
  HGHT: v3_ActCode_HGHT,
  /**
   * Healthcare encounter that takes place in the residence of the patient or a designee
   */
  HomeHealth: v3_ActCode_HomeHealth,
  /**
   * Indicates that the observation is of a person’s living situation in a household including the household composition and circumstances.
   */
  HouseholdSituationObservation: v3_ActCode_HouseholdSituationObservation,
  /**
   * Definition: A health insurance policy that covers healthcare benefits by protecting covered parties from medical expenses arising from health conditions, sickness, or accidental injury as well as preventive care. Health insurance policies explicitly exclude coverage for losses insured under a disability policy, workers' compensation program, liability insurance (including automobile insurance); or for medical expenses, coverage for on-site medical clinics or for limited dental or vision benefits when these are provided under a separate policy.
   *
   *
   *                            Discussion: Health insurance policies are offered by health insurance plans that typically reimburse providers for covered services on a fee-for-service basis, that is, a fee that is the allowable amount that a provider may charge.  This is in contrast to managed care plans, which typically prepay providers a per-member/per-month amount or capitation as reimbursement for all covered services rendered.  Health insurance plans include indemnity and healthcare services plans.
   */
  HealthInsurancePlanPolicy: v3_ActCode_HealthInsurancePlanPolicy,
  /**
   * The U.S. Public Law 104-191 Health Insurance Portability and Accountability Act (HIPAA) Privacy Rule (45 CFR Part 164 Subpart E) permits access, use and disclosure of certain personal health information (PHI as defined under the law) for purposes of Treatment, Payment, and Operations, and requires that the provider ask that patients acknowledge the Provider's Notice of Privacy Practices as permitted conduct under the law.
   *
   *
   *                            Definition: Notification of HIPAA Privacy Practices.
   *
   *
   *                            Usage Note: May be associated with an Act or a Role to indicate the legal provision to which the assignment of an Act.confidentialityCode or Role.confidentialtyCode complies.
   */
  HIPAANoticeOfPrivacyPractices: v3_ActCode_HIPAANoticeOfPrivacyPractices,
  /**
   * The U.S. Public Law 104-191 Health Insurance Portability and Accountability Act (HIPAA) Privacy Rule (45 CFR Part 164 Section 164.508) requires authorization for certain uses and disclosure of psychotherapy notes.
   *
   *
   *                            Definition: Authorization that must be obtained for disclosure of psychotherapy notes.
   *
   *
   *                            Usage Note: May be associated with an Act or a Role to indicate the legal provision to which the assignment of an Act.confidentialityCode or Role.confidentialityCode complies.
   */
  HIPAAPsychotherapyNotes: v3_ActCode_HIPAAPsychotherapyNotes,
  /**
   * Section 13405(a) of the Health Information Technology for Economic and Clinical Health Act (HITECH) stipulates the right of an individual to have disclosures regarding certain health care items or services for which the individual pays out of pocket in full restricted from a health plan.
   *
   *
   *                            Definition: Non-disclosure of health information to a health plan relating to health care items or services for which an individual pays out of pocket in full.
   *
   *
   *                            Usage Note: May be associated with an Act or a Role to indicate the legal provision to which the assignment of an Act.confidentialityCode or Role.confidentialityCode complies.
   */
  HIPAASelfPay: v3_ActCode_HIPAASelfPay,
  /**
   * Definition: A government program that provides health coverage to individuals who are considered medically uninsurable or high risk, and who have been denied health insurance due to a serious health condition. In certain cases, it also applies to those who have been quoted very high premiums a" again, due to a serious health condition.  The pool charges premiums for coverage.  Because the pool covers high-risk people, it incurs a higher level of claims than premiums can cover. The insurance industry pays into the pool to make up the difference and help it remain viable.
   */
  HighRiskPoolProgram: v3_ActCode_HighRiskPoolProgram,
  /**
   * Historical list of medications.
   */
  MedicationHistory: v3_ActCode_MedicationHistory,
  /**
   * Description: While the record was accepted in the repository, there is a more recent version of a record of this type.
   */
  RecordRecordedAsHistorical: v3_ActCode_RecordRecordedAsHistorical,
  /**
   * Policy for handling HIV or AIDS information, which will be afforded heightened confidentiality. Information handling protocols based on organizational policies related to HIV or AIDS information that is deemed sensitive.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  HIVAIDSInformationSensitivity: v3_ActCode_HIVAIDSInformationSensitivity,
  /**
   * Definition: Government administered and funded HIV-AIDS program for beneficiaries meeting financial and health status criteria.  Administration, funding levels, eligibility criteria, covered benefits, provider types, and financial participation are typically set by a regulatory process.  Payer responsibilities for administering the program may be delegated to contractors.
   *
   *
   *                            Example: In the U.S., the Ryan White program, which is administered by the Health Resources and Services Administration.
   */
  HIVAIDSProgram: v3_ActCode_HIVAIDSProgram,
  /**
   * Description: Exposure participants' interaction occurred during the course of health care delivery or in a health care delivery setting, but did not involve the direct provision of care (e.g. a janitor cleaning a patient's hospital room).
   */
  HealthCareInteractionNotPatientCare: v3_ActCode_HealthCareInteractionNotPatientCare,
  /**
   * Definition: A policy for a health plan that provides coverage for health care only through contracted or employed physicians and hospitals located in particular geographic or service areas.  HMOs emphasize prevention and early detection of illness. Eligibility to enroll in an HMO is determined by where a covered party lives or works.
   */
  HealthMaintenanceOrganizationPolicy: v3_ActCode_HealthMaintenanceOrganizationPolicy,
  /**
   * Description: Exposure interaction occurred in context of one providing care for the other, i.e. a babysitter providing care for a child, a home-care aide providing assistance to a paraplegic.
   */
  CareGiverInteraction: v3_ActCode_CareGiverInteraction,
  /**
   * Description: Exposure participants' interaction occurred when both were patients being treated in the same (acute) health care delivery facility.
   */
  HospitalPatientInteraction: v3_ActCode_HospitalPatientInteraction,
  /**
   * Description: Exposure participants' interaction occurred when one visited the other who was a patient being treated in a health care delivery facility.
   */
  HospitalVisitorInteraction: v3_ActCode_HospitalVisitorInteraction,
  /**
   * Description: Exposure interaction occurred in context of domestic interaction, i.e. both participants reside in the same household.
   */
  HouseholdInteraction: v3_ActCode_HouseholdInteraction,
  /**
   * A security category label field value, which indicates that access and use of an IT resource is restricted to members of human resources department or workflow.
   */
  HumanResourceCompartment: v3_ActCode_HumanResourceCompartment,
  /**
   * Insurance policy that provides for an allotment of funds replenished on a periodic (e.g. annual) basis. The use of the funds under this policy is at the 	discretion of the covered party.
   */
  HealthSpendingAccount: v3_ActCode_HealthSpendingAccount,
  /**
   * Joint Federal/Provincial Sales Tax
   */
  HarmonizedSalesTax: v3_ActCode_HarmonizedSalesTax,
  /**
   * Custodian system must require human review and approval for permission requested.
   */
  HumanApproval: v3_ActCode_HumanApproval,
  /**
   * Communication of an agent from a person to a proximate person.
   */
  HumanToHumanTransmission: v3_ActCode_HumanToHumanTransmission,
  /**
   * Accommodations used in the care of diseases that are transmitted through casual contact or respiratory transmission.
   */
  Isolation: v3_ActCode_Isolation,
  /**
   * Definition: Consent to have healthcare information collected in an electronic health record.  This entails that the information may be used in analysis, modified, updated.
   */
  InformationCollection: v3_ActCode_InformationCollection,
  /**
   * An observation that describes the icterus index of the specimen.  It is recommended to use mMol/L of bilirubin
   */
  Icterus: v3_ActCode_Icterus,
  /**
   * Used by one system to inform another that it has received a container.
   */
  Identified: v3_ActCode_Identified,
  /**
   * Definition: Consent to have collected healthcare information disclosed.
   */
  InformationDisclosure: v3_ActCode_InformationDisclosure,
  /**
   * Information on whether an increase or decrease in score is the preferred result
   * (e.g., a higher score indicates better quality OR a lower score indicates better quality OR quality is within a range).
   */
  ImprovementNotation: v3_ActCode_ImprovementNotation,
  /**
   * Description:The request is missing elements or contains elements which cause it to not meet the legal standards for actioning.
   */
  Illegal: v3_ActCode_Illegal,
  /**
   * Description: Image Attachment
   */
  ImageAttachment: v3_ActCode_ImageAttachment,
  /**
   * Description: A person enters an immunization due or received for a given patient.
   */
  ImmunizationListEntry: v3_ActCode_ImmunizationListEntry,
  /**
   * Description: A person reviews a list of immunizations due or received for a given patient.
   */
  ImmunizationListReview: v3_ActCode_ImmunizationListReview,
  /**
   * Definition:All information pertaining to a patient's vaccination records.
   */
  ImmunizationCategory: v3_ActCode_ImmunizationCategory,
  /**
   * The introduction of an immunogen with the intent of stimulating an immune response, aimed at preventing subsequent infections by more viable agents.
   */
  Immunization: v3_ActCode_Immunization,
  /**
   * A patient encounter where a patient is admitted by a hospital or equivalent facility, assigned to a location where patients generally stay at least overnight and provided with room, board, and continuous nursing service.
   */
  InpatientEncounter: v3_ActCode_InpatientEncounter,
  /**
   * A grantor's presumed assent to the grantee's terms of agreement is based on the grantor's behavior, which may result from not expressly assenting to the consent directive offered, or from having no right to assent or dissent offered by the grantee.
   *
   *
   *                            Comment: Implied or "implicit" consent occurs when the behavior of the grantor is understood by a reasonable person to signal agreement to the grantee's terms.
   *
   *
   *                            Usage Note: Implied consent with no opportunity to assent or dissent to certain terms is considered "basic consent".
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare: A patient schedules an appointment with a provider, and either does not take the opportunity to expressly assent or dissent to the provider's consent directive, does not have an opportunity to do so, as in the case where emergency care is required, or simply behaves as though the patient [grantor] agrees to the rights granted to the provider [grantee] in an implicit consent directive.
   *                            An injured and unconscious patient is deemed to have assented to emergency treatment by those permitted to do so under jurisdictional laws, e.g., Good Samaritan laws.
   *                            Non-healthcare: Upon receiving a driver's license, the driver is deemed to have assented without explicitly consenting to undergoing field sobriety tests.
   *                            A corporation that does business in a foreign nation is deemed to have deemed to have assented without explicitly consenting to abide by that nation's laws.
   */
  ImpliedConsent: v3_ActCode_ImpliedConsent,
  /**
   * A grantor's presumed assent to the grantee's terms of agreement, which is based on the grantor's behavior, and includes a right to dissent to certain terms.
   *
   *
   *                            Comment: A grantor assenting to the grantee's terms of agreement may or may not exercise a right to dissent to grantor selected terms or to grantee's selected terms to which a grantor may dissent.
   *
   *
   *                            Usage Note: Implied or "implicit" consent with an "opportunity to dissent" occurs when the grantor's behavior is understood by a reasonable person to signal assent to the grantee's terms of agreement whether the grantor requests or the grantee approves further restrictions, is considered "granular consent".
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare Examples: A healthcare provider deems a patient's assent to disclosure of health information to family members and friends, but offers an opportunity or permits the patient to dissent to such disclosures.
   *                            A health information exchanges deems a patient to have assented to disclosure of health information for treatment purposes, but offers the patient an opportunity to dissents to disclosure to particular provider organizations.
   *                            Non-healthcare Examples: A bank deems a banking customer's assent to specified collection, access, use, or disclosure of financial information as a requirement of holding a bank account, but provides the user an opportunity to limit third-party collection, access, use or disclosure of that information for marketing purposes.
   */
  ImpliedConsentWithOpportunityToDissent: v3_ActCode_ImpliedConsentWithOpportunityToDissent,
  /**
   * Definition: Services provided directly and through contracted and operated indigenous peoples health programs.
   *
   *
   *                            Example: Indian Health Service in the U.S.
   */
  IndigenousPeoplesHealthProgram: v3_ActCode_IndigenousPeoplesHealthProgram,
  /**
   * Description:Contrast agent required for imaging study.
   */
  ImagingStudyRequiringContrast: v3_ActCode_ImagingStudyRequiringContrast,
  /**
   * Description:Provision of prescription or direction to consume a product for purposes of bowel clearance in preparation for a colonoscopy.
   */
  ColonoscopyPrep: v3_ActCode_ColonoscopyPrep,
  /**
   * Description:Provision of medication as a preventative measure during a treatment or other period of increased risk.
   */
  Prophylaxis: v3_ActCode_Prophylaxis,
  /**
   * Description:Provision of medication during pre-operative phase; e.g., antibiotics before dental surgery or bowel prep before colon surgery.
   */
  SurgicalProphylaxis: v3_ActCode_SurgicalProphylaxis,
  /**
   * Description:Provision of medication for pregnancy --e.g., vitamins, antibiotic treatments for vaginal tract colonization, etc.
   */
  PregnancyProphylaxis: v3_ActCode_PregnancyProphylaxis,
  /**
   * Communication of an agent to a living subject via an undetermined route.
   */
  IndeterminateDiseaseTransmissionMode: v3_ActCode_IndeterminateDiseaseTransmissionMode,
  /**
   * Definition: Consent to access healthcare information.
   */
  InformationAccess: v3_ActCode_InformationAccess,
  /**
   * Definition: Consent to access or "read" only, which entails that the information is not to be copied, screen printed, saved, emailed, stored, re-disclosed or altered in any way.  This level ensures that data which is masked or to which access is restricted will not be.
   *
   *
   *                            Example: Opened and then emailed or screen printed for use outside of the consent directive purpose.
   */
  AccessOnly: v3_ActCode_AccessOnly,
  /**
   * Definition: Consent to access and save only, which entails that access to the saved copy will remain locked.
   */
  AccessAndSaveOnly: v3_ActCode_AccessAndSaveOnly,
  /**
   * Authorization to collect, access, use, or disclose specified patient health information in accordance with jurisdictional law, organizational policy, or a patient's consent directive, which may be implied, deemed, opt-in, opt-out, or explicit.
   */
  AuthorizedInformationTransfer: v3_ActCode_AuthorizedInformationTransfer,
  /**
   * Authorization to collect, access, use, or disclose specified patient health information as explicitly consented to by the subject of the information or the subject's representative.
   */
  AfterExplicitConsent: v3_ActCode_AfterExplicitConsent,
  /**
   * Authorization to collect, access, use, or disclose specified patient health information in accordance with judicial system protocol, such as in the case of a subpoena or court order.
   */
  OnlyOnCourtOrder: v3_ActCode_OnlyOnCourtOrder,
  /**
   * Authorization to collect, access, use, or disclose specified patient health information where deemed necessary to avert potential danger to other persons in accordance with jurisdictional law, organizational policy, or standards of practice.  For example, disclosure about a person threatening violence.
   */
  OnlyIfDangerToOthers: v3_ActCode_OnlyIfDangerToOthers,
  /**
   * Authorization to collect, access, use, or disclose specified patient health information in accordance with emergency information transfer protocol dictated by jurisdictional law, organization policy, or standards of practice. For example, sharing of health information during disaster response.
   */
  OnlyInAnEmergency: v3_ActCode_OnlyInAnEmergency,
  /**
   * Authorization to collect, access, use, or disclose specified patient health information necessary to avert potential public welfare risk in accordance with jurisdictional law, organizational policy, or standards of practice.  For example, reporting that a person is a victim of abuse or demonstrating suicidal tendencies.
   */
  OnlyIfPublicWelfareRisk: v3_ActCode_OnlyIfPublicWelfareRisk,
  /**
   * Authorization to collect, access, use, or disclose specified patient health information for public health, welfare, and safety purposes in accordance with jurisdictional law, organizational policy, or standards of practice.  For example, public health reporting of notifiable conditions.
   */
  RegulatoryInformationTransfer: v3_ActCode_RegulatoryInformationTransfer,
  /**
   * The initial quantity of the specimen in inventory
   */
  InitialVolume: v3_ActCode_InitialVolume,
  /**
   * The first immunization administration in a series intended to produce immunity
   */
  InitialImmunization: v3_ActCode_InitialImmunization,
  /**
   * Description: Exposure participants' interaction occurred in the course of one or both participants being incarcerated at a correctional facility
   */
  InmateInteraction: v3_ActCode_InmateInteraction,
  /**
   * Proposed therapy may be inappropriate or contraindicated because of a recorded patient intolerance to the proposed product.  (Intolerances are non-immune based sensitivities.)
   */
  IntoleranceAlert: v3_ActCode_IntoleranceAlert,
  /**
   * Intermediate diagnoses are those diagnoses documented for administrative purposes during the course of a hospital stay.
   */
  IntermediateDiagnosis: v3_ActCode_IntermediateDiagnosis,
  /**
   * Definition:The therapy action is being performed outside the bounds of the time period requested
   */
  OutsideRequestedTime: v3_ActCode_OutsideRequestedTime,
  /**
   * An observation that relates to factors that may potentially cause interference with the observation
   */
  ActSpecObsInterferenceCode: v3_ActCode_ActSpecObsInterferenceCode,
  /**
   * Description: Exposure interaction was intimate, i.e. participants are intimate companions (e.g. spouses, domestic partners).
   */
  IntimateInteraction: v3_ActCode_IntimateInteraction,
  /**
   * List of intolerance observations.
   */
  IntoleranceList: v3_ActCode_IntoleranceList,
  /**
   * Definition:A monitoring program that focuses on a drug which is under investigation and has not received regulatory approval for the condition being investigated
   */
  Investigational: v3_ActCode_Investigational,
  /**
   * Payment is based on a payment intent for a previously submitted Invoice, based on formal adjudication results..
   */
  SubmittedInvoice: v3_ActCode_SubmittedInvoice,
  /**
   * Transaction counts and value totals by invoice type (e.g. RXDINV - Pharmacy Dispense)
   */
  InvoiceType: v3_ActCode_InvoiceType,
  /**
   * Used by one system to inform another that the container is in position for specimen transfer (e.g., container removal from track, pipetting, etc.).
   */
  InPosition: v3_ActCode_InPosition,
  /**
   * Criteria for specifying the entities to be evaluated by a specific quality measure, based on a shared common set of characteristics (within a specific measurement set to which a given measure belongs).
   */
  InitialPopulation: v3_ActCode_InitialPopulation,
  /**
   * Criteria for specifying the patients to be evaluated by a specific quality measure, based on a shared common set of characteristics (within a specific measurement set to which a given measure belongs). Details often include information based upon specific age groups, diagnoses, diagnostic and procedure codes, and enrollment periods.
   */
  InitialPatientPopulation: v3_ActCode_InitialPatientPopulation,
  /**
   * Definition: Information re-disclosed without the patient's consent.
   */
  InformationRedisclosure: v3_ActCode_InformationRedisclosure,
  /**
   * Premium paid on service fees in compensation for practicing in a remote location.
   */
  IsolationAllowance: v3_ActCode_IsolationAllowance,
  /**
   * There is a clinical issue for the therapy that makes continuation of the therapy inappropriate.
   *
   *
   *                            Open Issue: The definition of this code does not correctly represent the concept space of its specializations (children)
   */
  DetectedIssue: v3_ActCode_DetectedIssue,
  /**
   * Describes the items counted by the measure (e.g., patients, encounters, procedures, etc.)
   */
  ItemsCounted: v3_ActCode_ItemsCounted,
  /**
   * A significant word that aids in discoverability.
   */
  Keyword: v3_ActCode_Keyword,
  /**
   * The ID of the patient, order, etc., was not found. Used for transactions other than additions, e.g. transfer of a non-existent patient.
   */
  UnknownKeyIdentifier: v3_ActCode_UnknownKeyIdentifier,
  /**
   * The ID of the patient, order, etc., already exists. Used in response to addition transactions (Admit, New Order, etc.).
   */
  DuplicateKeyIdentifier: v3_ActCode_DuplicateKeyIdentifier,
  /**
   * Description: Metadata associated with the identification (e.g. name or gender) does not match the identification being verified.
   */
  NonMatchingIdentification: v3_ActCode_NonMatchingIdentification,
  /**
   * Categorization of types of observation that capture the main clinical knowledge subject which may be a medication, a laboratory test, a disease.
   */
  KnowledgeSubject: v3_ActCode_KnowledgeSubject,
  /**
   * Categorization of types of observation that capture a knowledge subtopic which might be treatment, etiology, or prognosis.
   */
  KnowledgeSubtopic: v3_ActCode_KnowledgeSubtopic,
  /**
   * Used by one system to inform another that the container has been released from that system.
   */
  LeftEquipment: v3_ActCode_LeftEquipment,
  /**
   * Proposed therapy may be inappropriate or contraindicated due to recent lab test results
   */
  LabAlert: v3_ActCode_LabAlert,
  /**
   * Description: All information pertaining to a patient's lab test records (orders & results)
   */
  LabTestCategory: v3_ActCode_LabTestCategory,
  /**
   * Custodian security system must assign and bind security labels in order to classify information created in the information systems under its control for collection, access, use and disclosure in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the assignment and binding.
   *
   *
   *                            Usage Note: In security systems, security policy label assignments do not change, they may supersede prior assignments, and such reassignments are always tracked for auditing and other purposes.
   */
  AssignSecurityLabel: v3_ActCode_AssignSecurityLabel,
  /**
   * A clinician creates a request for a laboratory test to be done for a given patient.
   */
  LaboratoryTestOrderEntryTask: v3_ActCode_LaboratoryTestOrderEntryTask,
  /**
   * Description: Lab Results Attachment
   */
  LabResults: v3_ActCode_LabResults,
  /**
   * A person reviews a list of laboratory results of a given patient.
   */
  LaboratoryResultsReviewTask: v3_ActCode_LaboratoryResultsReviewTask,
  /**
   * Proposed therapy may be inappropriate or contraindicated when breast-feeding
   */
  LactationAlert: v3_ActCode_LactationAlert,
  /**
   * Communication of an agent from one living subject to another living subject through direct contact with mammalian milk or colostrum.
   */
  LactationTransmission: v3_ActCode_LactationTransmission,
  /**
   * Knowingly over the payor's published time limit for this invoice possibly due to a previous payor's delays in processing. Additional reason information will be supplied.
   */
  LateInvoice: v3_ActCode_LateInvoice,
  /**
   * law enforcement transport
   */
  LawEnforcementTransport: v3_ActCode_LawEnforcementTransport,
  /**
   * LDL Precipitation
   */
  LDLPrecipitation: v3_ActCode_LDLPrecipitation,
  /**
   * Description:The length of the data specified is greater than the maximum length defined for the element.
   */
  LengthIsTooLong: v3_ActCode_LengthIsTooLong,
  /**
   * Description:The length of the data specified falls out of the range defined for the element.
   */
  LengthOutOfRange: v3_ActCode_LengthOutOfRange,
  /**
   * Description:The length of the data specified is less than the minimum length defined for the element.
   */
  LengthIsTooShort: v3_ActCode_LengthIsTooShort,
  /**
   * A grouping of invoice element details including the ones specifying the lens fee and the lens dispensing cost that are being invoiced.
   */
  LensInvoiceGroup: v3_ActCode_LensInvoiceGroup,
  /**
   * A diet low in fat, particularly to patients with hepatic diseases.
   */
  LowFat: v3_ActCode_LowFat,
  /**
   * Definition: Maximum amount paid by payer or covered party; or maximum number of services or products covered under the policy or program during a covered party's lifetime.
   */
  LifeTimeMaximum: v3_ActCode_LifeTimeMaximum,
  /**
   * Description:Scope of responsibility taken-on for physician care of a patient as defined by a governmental licensing agency.
   */
  LicensedGeneralPhysicianCare: v3_ActCode_LicensedGeneralPhysicianCare,
  /**
   * Definition: A policy under which the insurer agrees to pay a sum of money upon the occurrence of the covered partys death. In return, the policyholder agrees to pay a stipulated amount called a premium at regular intervals.  Life insurance indemnifies the beneficiary for the loss of the insurable interest that a beneficiary has in the life of a covered party.  For persons related by blood, a substantial interest established through love and affection, and for all other persons, a lawful and substantial economic interest in having the life of the insured continue. An insurable interest is required when purchasing life insurance on another person. Specific exclusions are often written into the contract to limit the liability of the insurer; for example claims resulting from suicide or relating to war, riot and civil commotion.
   *
   *
   *                            Discussion:A life insurance policy may be used by the covered party as a source of health care coverage in the case of  a viatical settlement, which is the sale of a life insurance policy by the policy owner, before the policy matures. Such a sale, at a price discounted from the face amount of the policy but usually in excess of the premiums paid or current cash surrender value, provides the seller an immediate cash settlement. Generally, viatical settlements involve insured individuals with a life expectancy of less than two years. In countries without state-subsidized healthcare and high healthcare costs (e.g. United States), this is a practical way to pay extremely high health insurance premiums that severely ill people face. Some people are also familiar with life settlements, which are similar transactions but involve insureds with longer life expectancies (two to fifteen years).
   */
  LifeInsurancePolicy: v3_ActCode_LifeInsurancePolicy,
  /**
   * An observation used to describe the Lipemia Index of the specimen. It is recommended to use the optical turbidity at 600 nm (in absorbance units).
   */
  Lipemia: v3_ActCode_Lipemia,
  /**
   * Policy for handling information related to an information subject's living arrangement, which will be afforded heightened confidentiality.  Policies may govern sensitivity of information related to an information subject's living arrangement, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  LivingArrangementInformationSensitivity: v3_ActCode_LivingArrangementInformationSensitivity,
  /**
   * Temporary supply of a product without transfer of ownership for the product.
   */
  Loan: v3_ActCode_Loan,
  /**
   * Transaction counts and value totals by service location (e.g clinic).
   */
  Location: v3_ActCode_Location,
  /**
   * Policy for handling information related to the location of the information subject, which will be afforded heightened confidentiality.  Description:  Policies may govern sensitivity of information related to the location of the information subject, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Notes: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  LocationInformationSensitivity: v3_ActCode_LocationInformationSensitivity,
  /**
   * A low protein diet for patients with renal failure.
   */
  LowProtein: v3_ActCode_LowProtein,
  /**
   * A strictly liquid diet, that can be fully absorbed in the intestine, and therefore may not contain fiber.  Used before enteral surgeries.
   */
  Liquid: v3_ActCode_Liquid,
  /**
   * Providers and care givers who have an established relationship per criteria determined by policy are considered to have an established care provision relations with a healthcare consumer, and may be authorized to access the consumer's health information because of that relationship.  Providers and care givers should only have access to that information while participating in legitimate relationship workflows or for other authorized uses.
   *
   *                         Security Compartment Labels assigned to a consumer's information use in legitimate relationship workflows should be met or exceeded by the Security Compartment attribute claimed by a participant in a legitimate relationship workflow who is requesting access to that information.
   */
  LegitimateRelationshipCompartment: v3_ActCode_LegitimateRelationshipCompartment,
  /**
   * A diet low in sodium for patients with congestive heart failure and/or renal failure.
   */
  LowSodium: v3_ActCode_LowSodium,
  /**
   * Definition: An insurance policy that covers benefits for long-term care services people need when they no longer can care for themselves. This may be due to an accident, disability, prolonged illness or the simple process of aging. Long-term care services assist with activities of daily living including:
   *
   *
   *
   *                               Help at home with day-to-day activities, such as cooking, cleaning, bathing and dressing
   *
   *
   *
   *                               Care in the community, such as in an adult day care facility
   *
   *
   *
   *                               Supervised care provided in an assisted living facility
   *
   *
   *
   *                               Skilled care provided in a nursing home
   */
  LongTermCarePolicy: v3_ActCode_LongTermCarePolicy,
  /**
   * Description: Exposure participants' interaction occurred in the course of one or both participants being resident at a long term care facility (second participant may be a visitor, worker, resident or a physical place or object within the facility).
   */
  LongTermCareFacilityInteraction: v3_ActCode_LongTermCareFacilityInteraction,
  /**
   * Description:A drug that can be prescribed (and reimbursed) only if it meets certain criteria.
   */
  LimitedUse: v3_ActCode_LimitedUse,
  /**
   * Used by one system to inform another that the container did not arrive at its next expected location.
   */
  Missing: v3_ActCode_Missing,
  /**
   * mandatory health program
   */
  MandatoryHealthProgram: v3_ActCode_MandatoryHealthProgram,
  /**
   * Manual review of the invoice is requested.  Additional information to be supplied.  This may be used in the case of an appeal.
   */
  ManualReview: v3_ActCode_ManualReview,
  /**
   * An increase in the amount charged as a percentage of the amount. For example, 12% markup on product cost.
   */
  MarkupOrUpCharge: v3_ActCode_MarkupOrUpCharge,
  /**
   * Policy for handling information related to an information subject's marital status, which will be afforded heightened confidentiality. Policies may govern sensitivity of information related to an information subject's marital status, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  MaritalStatusInformationSensitivity: v3_ActCode_MaritalStatusInformationSensitivity,
  /**
   * A clinician reviews a work list of medications to be administered to a given patient.
   */
  MedicationAdministrationRecordWorkListReviewTask: v3_ActCode_MedicationAdministrationRecordWorkListReviewTask,
  /**
   * Custodian system must render information unreadable and unusable by algorithmically transforming plaintext into ciphertext.  User may be provided a key to decrypt per license or "shared secret".
   */
  Mask: v3_ActCode_Mask,
  /**
   * Description:The number of repeating elements is above the maximum number of repetitions allowed.
   */
  RepetitionsAboveMaximum: v3_ActCode_RepetitionsAboveMaximum,
  /**
   * Master Card
   */
  MasterCard: v3_ActCode_MasterCard,
  /**
   * Definition: Government mandated program providing coverage, disability income, and vocational rehabilitation for injuries sustained in the work place or in the course of employment.  Employers may either self-fund the program, purchase commercial coverage, or pay a premium to a government entity that administers the program.  Employees may be required to pay premiums toward the cost of coverage as well.
   *
   *                         Managed care policies specifically exclude coverage for losses insured under a disability policy, workers' compensation program, liability insurance (including automobile insurance); or for medical expenses, coverage for on-site medical clinics or for limited dental or vision benefits when these are provided under a separate policy.
   *
   *
   *                            Discussion: Managed care policies are offered by managed care plans that contract with selected providers or health care organizations to provide comprehensive health care at a discount to covered parties and coordinate the financing and delivery of health care. Managed care uses medical protocols and procedures agreed on by the medical profession to be cost effective, also known as medical practice guidelines. Providers are typically reimbursed for covered services by a capitated amount on a per member per month basis that may reflect difference in the health status and level of services anticipated to be needed by the member.
   */
  ManagedCarePolicy: v3_ActCode_ManagedCarePolicy,
  /**
   * Description:The maximum quantity of this drug allowed to be administered within a particular time-range (month, year, lifetime) has been reached or exceeded.
   */
  MaximumDosageReached: v3_ActCode_MaximumDosageReached,
  /**
   * Provision of diagnostic and/or therapeutic treatment.
   */
  Medical: v3_ActCode_Medical,
  /**
   * Definition:All information pertaining to a patient's medical condition records.
   */
  MedicalConditionCategory: v3_ActCode_MedicalConditionCategory,
  /**
   * List of medications.
   */
  MedicationList: v3_ActCode_MedicationList,
  /**
   * A clinician creates a request for the administration of one or more medications to a given patient.
   */
  MedicationOrderEntryTask: v3_ActCode_MedicationOrderEntryTask,
  /**
   * The end date of the measurement period.
   */
  MeasurementEndDate: v3_ActCode_MeasurementEndDate,
  /**
   * Description: All information pertaining to a patient's mental health records.
   */
  MentalHealthCategory: v3_ActCode_MentalHealthCategory,
  /**
   * Definition: A health insurance policy that covers benefits for mental health services and prescriptions.
   */
  MentalHealthPolicy: v3_ActCode_MentalHealthPolicy,
  /**
   * Definition: Government administered and funded mental health program for beneficiaries meeting financial and mental health status criteria.  Administration, funding levels, eligibility criteria, covered benefits, provider types, and financial participation are typically set by a regulatory process.  Payer responsibilities for administering the program may be delegated to contractors.
   *
   *
   *                            Example: In the U.S., states receive funding for substance use programs from the Substance Abuse Mental Health Administration (SAMHSA).
   */
  MentalHealthProgram: v3_ActCode_MentalHealthProgram,
  /**
   * Policy for handling information related to psychological disorders, which is afforded heightened confidentiality. Mental health information may be deemed specifically sensitive and distinct from physical health, substance use disorders, and behavioral disabilities and disorders in some jurisdictions.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  MentalHealthInformationSensitivity: v3_ActCode_MentalHealthInformationSensitivity,
  /**
   * A person reviews organisms of microbiology results of a given patient.
   */
  MicrobiologyOrganismsResultsReviewTask: v3_ActCode_MicrobiologyOrganismsResultsReviewTask,
  /**
   * A person reviews a list of microbiology results of a given patient.
   */
  MicrobiologyResultsReviewTask: v3_ActCode_MicrobiologyResultsReviewTask,
  /**
   * A person reviews the sensitivity test of microbiology results of a given patient.
   */
  MicrobiologySensitivityTestResultsReviewTask: v3_ActCode_MicrobiologySensitivityTestResultsReviewTask,
  /**
   * Definition: A government program that provides coverage for health services to military personnel, retirees, and dependents.  A covered party who is a subscriber can choose from among Fee-for-Service (FFS) plans, and their Preferred Provider Organizations (PPO), or Plans offering a Point of Service (POS) Product, or Health Maintenance Organizations.
   *
   *
   *                            Example: In the U.S., TRICARE, CHAMPUS.
   */
  MilitaryHealthProgram: v3_ActCode_MilitaryHealthProgram,
  /**
   * Custodian must limit access and disclosure to the minimum information required to support an authorized user's purpose of use.
   *
   *
   *                            Usage Note: Limiting the information available for access and disclosure to that an authorized user or receiver "needs to know" in order to perform permitted workflow or purpose of use.
   */
  MinimumNecessary: v3_ActCode_MinimumNecessary,
  /**
   * Definition:The therapy action is being performed too soon after the previous occurrence based on the requested frequency
   */
  TooSoonWithinFrequencyBasedOnTheUsage: v3_ActCode_TooSoonWithinFrequencyBasedOnTheUsage,
  /**
   * Description:The number of repeating elements is below the minimum number of repetitions allowed.
   */
  RepetitionsBelowMinimum: v3_ActCode_RepetitionsBelowMinimum,
  /**
   * A charge to compensate the provider when a patient does not show for an appointment.
   */
  MissedAppointment: v3_ActCode_MissedAppointment,
  /**
   * Description:The specified element must be specified with a non-null value under certain conditions.  In this case, the conditions are true but the element is still missing or null.
   */
  ConditionalElementMissing: v3_ActCode_ConditionalElementMissing,
  /**
   * Description:The specified element is mandatory and was not included in the instance.
   */
  MandatoryElementMissing: v3_ActCode_MandatoryElementMissing,
  /**
   * A person reviews a list of medication orders submitted to a given patient
   */
  MedicationListReviewTask: v3_ActCode_MedicationListReviewTask,
  /**
   * Description: Digital Model Attachment
   */
  Model: v3_ActCode_Model,
  /**
   * Transaction counts and value totals for each calendar month within the date range specified.
   */
  Month: v3_ActCode_Month,
  /**
   * A supply of a manufacturer sample
   */
  ManufacturerSample: v3_ActCode_ManufacturerSample,
  /**
   * The start date of the measurement period.
   */
  MeasurementStartDate: v3_ActCode_MeasurementStartDate,
  /**
   * The method of adjusting for clinical severity and conditions present at the start of care that can influence patient outcomes for making valid comparisons of outcome measures across providers. Indicates whether an eMeasure is subject to the statistical process for reducing, removing, or clarifying the influences of confounding factors to allow more useful comparisons.
   */
  RiskAdjustment: v3_ActCode_RiskAdjustment,
  /**
   * Describes how to combine information calculated based on logic in each of several populations into one summarized result. It can also be used to describe how to risk adjust the data based on supplemental data elements described in the eMeasure. (e.g., pneumonia hospital measures antibiotic selection in the ICU versus non-ICU and then the roll-up of the two).
   *
   *
   *                            Open Issue: The description does NOT align well with the definition used in the HQMF specfication; correct the MSGAGG definition, and the possible distinction of MSRAGG as a child of AGGREGATE.
   */
  RateAggregation: v3_ActCode_RateAggregation,
  /**
   * Information on whether an increase or decrease in score is the preferred result. This should reflect information on which way is better, an increase or decrease in score.
   */
  HealthQualityMeasureImprovementNotation: v3_ActCode_HealthQualityMeasureImprovementNotation,
  /**
   * The list of jurisdiction(s) for which the measure applies.
   */
  Jurisdiction: v3_ActCode_Jurisdiction,
  /**
   * Defines the observation to be performed for each patient or event in the measure population. Measure observations for each case in the population are aggregated to determine the overall measure score for the population.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            the median time from arrival in the Emergency Room to departure
   *                            the median time from decision to admit to a hospital to the actual admission for Emergency Room patients
   */
  MeasureObservation: v3_ActCode_MeasureObservation,
  /**
   * Criteria for specifying
   * the measure population as a narrative description (e.g., all patients seen in the Emergency Department during the measurement period).  This is used only in continuous variable eMeasures.
   */
  MeasurePopulation: v3_ActCode_MeasurePopulation,
  /**
   * Criteria for specifying subjects who should be removed from the eMeasure's Initial Population and Measure Population. Measure Population Exclusions are used in Continuous Variable measures to help narrow the Measure Population before determining the value(s) of the continuous variable(s).
   */
  MeasurePopulationExclusions: v3_ActCode_MeasurePopulationExclusions,
  /**
   * Type of person or organization that is expected to report the issue.
   */
  ReporterType: v3_ActCode_ReporterType,
  /**
   * The maximum time that may elapse following completion of the measure until the measure report must be sent to the receiver.
   */
  TimeframeForReporting: v3_ActCode_TimeframeForReporting,
  /**
   * Indicates how the calculation is performed for the eMeasure
   * (e.g., proportion, continuous variable, ratio)
   */
  MeasureScoring: v3_ActCode_MeasureScoring,
  /**
   * Location(s) in which care being measured is rendered
   *
   *                         Usage Note: MSRSET is used rather than RoleCode because the setting applies to what is being measured, as opposed to participating directly in the health quality measure documantion itself).
   */
  HealthQualityMeasureCareSetting: v3_ActCode_HealthQualityMeasureCareSetting,
  /**
   * health quality measure topic type
   */
  HealthQualityMeasureTopicType: v3_ActCode_HealthQualityMeasureTopicType,
  /**
   * The time period for which the eMeasure applies.
   */
  MeasurementPeriod: v3_ActCode_MeasurementPeriod,
  /**
   * Indicates whether the eMeasure is used to examine a process or an outcome over time
   * (e.g., Structure, Process, Outcome).
   */
  MeasureType: v3_ActCode_MeasureType,
  /**
   * Policy for handling information related to sexual assault or repeated, threatening sexual harassment that occurred while the patient was in the military, which is afforded heightened confidentiality.
   *
   *                         Access control concerns for military sexual trauma is based on the patient being subject to control by a higher ranking military perpetrator and/or censure by others within the military unit.  Due to the relatively unfettered access to healthcare information by higher ranking military personnel and those who have command over the patient, there is a need to sequester this information outside of the typical controls on access to military health records.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  MilitarySexualTraumaInformationSensitivity: v3_ActCode_MilitarySexualTraumaInformationSensitivity,
  /**
   * Incident or accident as the result of a motor vehicle accident
   */
  MotorVehicleAccident: v3_ActCode_MotorVehicleAccident,
  /**
   * A normal diet, i.e. no special preparations or restrictions for medical reasons. This is notwithstanding any preferences the patient might have regarding special foods, such as vegetarian, kosher, etc.
   */
  NormalDiet: v3_ActCode_NormalDiet,
  /**
   * Hypersensitivity to an agent caused by a mechanism other than an immunologic response to an initial exposure
   */
  NonAllergyIntolerance: v3_ActCode_NonAllergyIntolerance,
  /**
   * The requesting party has insufficient authorization to invoke the interaction.
   */
  InsufficientAuthorization: v3_ActCode_InsufficientAuthorization,
  /**
   * Authorization for specified healthcare service(s) and/or product(s) denied.
   */
  NotAuthorized: v3_ActCode_NotAuthorized,
  /**
   * Insurance coverage is not in effect for healthcare service(s) and/or product(s). May optionally include reasons for the ineligibility.
   */
  NotEligible: v3_ActCode_NotEligible,
  /**
   * Maximum net amount that will be covered for the product or service specified.
   */
  NetAmount: v3_ActCode_NetAmount,
  /**
   * The act or process by which an acid and a base are combined in such proportions that the resulting compound is neutral.
   */
  Neutralization: v3_ActCode_Neutralization,
  /**
   * A no fat diet for acute hepatic diseases.
   */
  NoFat: v3_ActCode_NoFat,
  /**
   * Proposed therapy may interact with existing or recent natural health product therapy
   */
  NaturalHealthProductAlert: v3_ActCode_NaturalHealthProductAlert,
  /**
   * Prohibition on disclosure without information subject's authorization.
   */
  NoDisclosureWithoutSubjectAuthorization: v3_ActCode_NoDisclosureWithoutSubjectAuthorization,
  /**
   * Prohibition on collection or storage of the information.
   */
  NoCollection: v3_ActCode_NoCollection,
  /**
   * No notification or opportunity is provided for a grantor to assent or dissent to a grantee's terms of agreement.
   *
   *
   *                            Comment: A "No Consent" policy scheme provides no opportunity for accommodation of an individual's preferences, and may not comply with Fair Information Practice Principles [FIPP] by enabling the data subject to object, access collected information, correct errors, or have accounting of disclosures.
   *
   *
   *                            Usage Note: The grantee's terms of agreement, may be available to the grantor by reviewing the grantee's privacy policies, but there is no notice by which a grantor is apprised of the policy directly or able to acknowledge.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare: Without notification or an opportunity to assent or dissent, a patient's health information is automatically included in and available (often according to certain rules) through a health information exchange.  Note that this differs from implied consent, where the patient is assumed to have consented.
   *                            Without notification or an opportunity to assent or dissent, a patient's health information is collected, accessed, used, or disclosed for research, public health, security, fraud prevention, court order, or law enforcement.
   *                            Non-healthcare: Without notification or an opportunity to assent or dissent, a consumer's healthcare or non-healthcare internet searches are aggregated for secondary uses such as behavioral tracking and profiling.
   *                            Without notification or an opportunity to assent or dissent, a consumer's location and activities in a shopping mall are tracked by RFID tags on purchased items.
   */
  NoConsent: v3_ActCode_NoConsent,
  /**
   * Prohibition on disclosure without organizational approved patient restriction.
   */
  NoDisclosureWithoutConsentDirective: v3_ActCode_NoDisclosureWithoutConsentDirective,
  /**
   * Prohibition on disclosure without a consent directive from the information subject.
   */
  NoDisclosureWithoutInformationSubjectQuoteSConsentDirective: v3_ActCode_NoDisclosureWithoutInformationSubjectQuoteSConsentDirective,
  /**
   * Description:More than one element with the same value exists in the set.  Duplicates not permission in this set in a set.
   */
  DuplicateValuesAreNotPermitted: v3_ActCode_DuplicateValuesAreNotPermitted,
  /**
   * The type of injury that the injury coding specifies.
   */
  NatureOfInjury: v3_ActCode_NatureOfInjury,
  /**
   * Prohibition on Integration into other records.
   */
  NoIntegration: v3_ActCode_NoIntegration,
  /**
   * Prohibition on disclosure except to entities on specific access list.
   */
  NoUnlistedEntityDisclosure: v3_ActCode_NoUnlistedEntityDisclosure,
  /**
   * Prohibition on disclosure without an interagency service agreement or memorandum of understanding (MOU).
   */
  NoDisclosureWithoutMOU: v3_ActCode_NoDisclosureWithoutMOU,
  /**
   * Non-Payment Data.
   */
  NonPaymentData: v3_ActCode_NonPaymentData,
  /**
   * Any category of inpatient encounter except 'acute'
   */
  InpatientNonAcute: v3_ActCode_InpatientNonAcute,
  /**
   * Proposed therapy may interact with a non-prescription drug (e.g. alcohol, tobacco, Aspirin)
   */
  NonPrescriptionInteractionAlert: v3_ActCode_NonPrescriptionInteractionAlert,
  /**
   * Prohibition on disclosure without organizational authorization.
   */
  NoDisclosureWithoutOrganizationalAuthorization: v3_ActCode_NoDisclosureWithoutOrganizationalAuthorization,
  /**
   * Prohibition on disclosing information to patient, family or caregivers without attending provider's authorization.
   *
   *
   *                            Usage Note: The information may be labeled with the ActInformationSensitivity TBOO code, triggering application of this RefrainPolicy code as a handling caveat controlling access.
   *
   *                         Maps to FHIR NOPAT: Typically, this is used on an Alert resource, when the alert records information on patient abuse or non-compliance.
   *
   *                         FHIR print name is "keep information from patient". Maps to the French realm - code: INVISIBLE_PATIENT.
   *
   *
   *                            displayName: Document non visible par le patient
   *                            codingScheme: 1.2.250.1.213.1.1.4.13
   *
   *                         French use case:  A label for documents that the author  chose to hide from the patient until the content can be disclose to the patient in a face to face meeting between a healthcare professional and the patient (in French law some results like cancer diagnosis or AIDS diagnosis must be announced to the patient by a healthcare professional and should not be find out by the patient alone).
   */
  NoDisclosureToPatientFamilyOrCaregiversWithoutAttendingProviderQuoteSAuthorization: v3_ActCode_NoDisclosureToPatientFamilyOrCaregiversWithoutAttendingProviderQuoteSAuthorization,
  /**
   * Description: Element in submitted message will not persist in data storage based on detected issue.
   */
  ElementWillNotBePersisted: v3_ActCode_ElementWillNotBePersisted,
  /**
   * Prohibition on collection of the information beyond time necessary to accomplish authorized purpose of use is prohibited.
   */
  NoCollectionBeyondPurposeOfUse: v3_ActCode_NoCollectionBeyondPurposeOfUse,
  /**
   * Acknowledgement of custodian notice of privacy practices.
   *
   *
   *                            Usage Notes: This type of consent directive acknowledges a custodian's notice of privacy practices including its permitted collection, access, use and disclosure of health information to users and for purposes of use specified.
   */
  NoticeOfPrivacyPractices: v3_ActCode_NoticeOfPrivacyPractices,
  /**
   * Prohibition on redisclosure without patient consent directive.
   */
  NoRedisclosureWithoutConsentDirective: v3_ActCode_NoRedisclosureWithoutConsentDirective,
  /**
   * Prohibition on redisclosure without a consent directive from the information subject.
   */
  NoRedisclosureWithoutInformationSubjectQuoteSConsentDirective: v3_ActCode_NoRedisclosureWithoutInformationSubjectQuoteSConsentDirective,
  /**
   * Prohibition on disclosure without authorization under jurisdictional law.
   */
  NoDisclosureWithoutJurisdictionalAuthorization: v3_ActCode_NoDisclosureWithoutJurisdictionalAuthorization,
  /**
   * Prohibition on associating de-identified or pseudonymized information with other information in a manner that could or does result in disclosing information intended to be masked.
   */
  NoRelinking: v3_ActCode_NoRelinking,
  /**
   * Prohibition on use of the information beyond the purpose of use initially authorized.
   */
  NoReuseBeyondPurposeOfUse: v3_ActCode_NoReuseBeyondPurposeOfUse,
  /**
   * Communication of an agent from any entity to a living subject while the living subject is in the patient role in a healthcare facility.
   */
  NosocomialTransmission: v3_ActCode_NosocomialTransmission,
  /**
   * Definition:The status of the request being fulfilled has changed such that it is no longer actionable.  This may be because the request has expired, has already been completely fulfilled or has been otherwise stopped or disabled.  (Not used for 'suspended' orders.)
   */
  NoLongerActionable: v3_ActCode_NoLongerActionable,
  /**
   * Definition:The therapy being performed is not sufficiently equivalent to the therapy which was requested.
   */
  NotEquivalentAlert: v3_ActCode_NotEquivalentAlert,
  /**
   * Definition:The therapy being performed is not generically equivalent (having the identical biological action) to the therapy which was requested.
   */
  NotGenericallyEquivalentAlert: v3_ActCode_NotGenericallyEquivalentAlert,
  /**
   * Definition:The therapy being performed is not therapeutically equivalent (having the same overall patient effect) to the therapy which was requested.
   */
  NotTherapeuticallyEquivalentAlert: v3_ActCode_NotTherapeuticallyEquivalentAlert,
  /**
   * Prohibition on disclosure except to principals with access permission to specific VIP information.
   */
  NoUnauthorizedVIPDisclosure: v3_ActCode_NoUnauthorizedVIPDisclosure,
  /**
   * Criteria for specifying the processes or outcomes expected for each patient, procedure, or other unit of measurement defined in the denominator for proportion measures, or related to (but not directly derived from) the denominator for ratio measures (e.g., a numerator listing the number of central line blood stream infections and a denominator indicating the days per thousand of central line usage in a specific time period).
   */
  Numerator: v3_ActCode_Numerator,
  /**
   * Criteria for specifying instances that should not be included in the numerator data. (e.g., if the number of central line blood stream infections per 1000 catheter days were to exclude infections with a specific bacterium, that bacterium would be listed as a numerator exclusion).  Numerator Exclusions are used only in ratio eMeasures.
   */
  NumeratorExclusions: v3_ActCode_NumeratorExclusions,
  /**
   * Used by one system to inform another that the specific container is being processed by the equipment. It is useful as a response to a query about Container Status, when the specific step of the process is not relevant.
   */
  InProcess: v3_ActCode_InProcess,
  /**
   * Conveys the mandated workflow action that an information custodian, receiver, or user must perform.
   *
   *
   *                            Usage Notes: Per ISO 22600-2, ObligationPolicy instances 'are event-triggered and define actions to be performed by manager agent'. Per HL7 Composite Security and Privacy Domain Analysis Model:  This value set refers to the action required to receive the permission specified in the privacy rule. Per OASIS XACML, an obligation is an operation specified in a policy or policy that is performed in conjunction with the enforcement of an access control decision.
   */
  ObligationPolicy: v3_ActCode_ObligationPolicy,
  /**
   * Provision of care of women during pregnancy, childbirth and immediate postpartum period.  Also known as Maternity.
   */
  Obstetrics: v3_ActCode_Obstetrics,
  /**
   * Proposed therapy may be inappropriate or contraindicated due to conditions or characteristics of the patient
   */
  ObservationAlert: v3_ActCode_ObservationAlert,
  /**
   * Description: Indicates the valid antigen count.
   */
  AntigenCount: v3_ActCode_AntigenCount,
  /**
   * Description: Indicates whether an antigen is valid or invalid.
   */
  AntigenValidity: v3_ActCode_AntigenValidity,
  /**
   * An encounter where the patient usually will start in different encounter, such as one in the emergency department (EMER) but then transition to this type of encounter because they require a significant period of treatment and monitoring to determine whether or not their condition warrants an inpatient admission or discharge. In the majority of cases the decision about admission or discharge will occur within a time period determined by local, regional or national regulation, often between 24 and 48 hours.
   */
  ObservationEncounter: v3_ActCode_ObservationEncounter,
  /**
   * Description: One or more records in the query response have a status of 'obsolete'.
   */
  ObsoleteRecordReturned: v3_ActCode_ObsoleteRecordReturned,
  /**
   * A clinician creates a request for a service to be performed for a given patient.
   */
  OrderEntryTask: v3_ActCode_OrderEntryTask,
  /**
   * A clinical Invoice Grouping consisting of one or more oral health services. Billing for these service(s) are supported by multiple clinical billable events (acts).
   *
   *                         All items in the Invoice Grouping must be adjudicated together to be acceptable to the Adjudicator.
   */
  OralHealthService: v3_ActCode_OralHealthService,
  /**
   * Hypersensitivity resulting in an adverse reaction upon exposure to an agent.
   */
  Intolerance: v3_ActCode_Intolerance,
  /**
   * Provision of treatment and/or diagnosis related to tumors and/or cancer.
   */
  Oncology: v3_ActCode_Oncology,
  /**
   * Definition:A list of medications which the patient is intended to be administered only once.
   */
  OneTime: v3_ActCode_OneTime,
  /**
   * The medical service and/or product was provided to a patient that has coverage in another jurisdiction.
   */
  OutOfJurisdiction: v3_ActCode_OutOfJurisdiction,
  /**
   * Premium paid on service fees in compensation for practicing at a location other than normal working location.
   */
  OutOfOffice: v3_ActCode_OutOfOffice,
  /**
   * Policy for handling information related to opioid use disorders and conditions caused by these disorders, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  OpioidUseDisorderInformationSensitivity: v3_ActCode_OpioidUseDisorderInformationSensitivity,
  /**
   * A grantor's assent to the terms of an agreement offered by a grantee without an opportunity for to dissent to any terms.
   *
   *
   *                            Comment: Acceptance of a grantee's terms pertaining, for example, to permissible activities, purposes of use, handling caveats, expiry date, and revocation policies.
   *
   *
   *                            Usage Note: Opt-in with no opportunity for a grantor to restrict certain permissions sought by the grantee is considered "basic consent".
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare: A patient [grantor] signs a provider's [grantee's] consent directive form, which lists permissible collection, access, use, or disclosure activities, purposes of use, handling caveats, and revocation policies.
   *                            Non-healthcare: An employee [grantor] signs an employer's [grantee's] non-disclosure and non-compete agreement.
   */
  OptIn: v3_ActCode_OptIn,
  /**
   * A grantor's assent to the grantee's terms of an agreement with an opportunity for to dissent to certain grantor or grantee selected terms.
   *
   *
   *                            Comment: A grantor dissenting to the grantee's terms of agreement may or may not exercise a right to assent to grantor's pre-approved restrictions or to grantee's selected terms to which a grantor may dissent.
   *
   *
   *                            Usage Note: Opt-in with restrictions is considered "granular consent" because the grantor has an opportunity to narrow the permissions sought by the grantee.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare:  A patient assent to grantee's consent directive terms for collection, access, use, or disclosure of health information, and dissents to disclosure to certain recipients as allowed by the provider's pre-approved restriction list.
   *                            Non-Healthcare: A cell phone user assents to the cell phone's privacy practices and terms of use, but dissents from location tracking by turning off the cell phone's tracking capability.
   */
  OptInWithRestrictions: v3_ActCode_OptInWithRestrictions,
  /**
   * A grantor's dissent to the terms of agreement offered by a grantee without an opportunity for to assent to any terms.
   *
   *
   *                            Comment: Rejection of a grantee's terms of agreement pertaining, for example, to permissible activities, purposes of use, handling caveats, expiry date, and revocation policies.
   *
   *
   *                            Usage Note: Opt-out with no opportunity for a grantor to permit certain permissions sought by the grantee is considered "basic consent".
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare: A patient [grantor] declines to sign a provider's [grantee's] consent directive form, which lists permissible collection, access, use, or disclosure activities, purposes of use, handling caveats, revocation policies, and consequences of not assenting.
   *                            Non-healthcare: An employee [grantor] refuses to sign an employer's [grantee's] agreement not to join unions or participate in a strike where state law protects employee's collective bargaining rights.
   *                            A citizen [grantor] refuses to enroll in mandatory government [grantee] health insurance based on religious beliefs, which is an exemption.
   */
  OpOut: v3_ActCode_OpOut,
  /**
   * A grantor's dissent to the grantee's terms of agreement except for certain grantor or grantee selected terms.
   *
   *
   *                            Comment: A rejection of a grantee's terms of agreement while assenting to certain permissions sought by the grantee or requesting approval of additional grantor terms.
   *
   *
   *                            Usage Note: Opt-out with exceptions is considered a "granular consent" because the grantor has an opportunity to accept certain permissions sought by the grantee or request additional grantor terms, while rejecting other grantee terms.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare: A patient [grantor] dissents to a health information exchange consent directive with the exception of disclosure based on a limited "time to live" shared secret [e.g., a token or password], which the patient can give to a provider when seeking care.
   *                            Non-healthcare: A social media user [grantor] dissents from public access to their account, but assents to access to a circle of friends.
   */
  OptOutWithExceptions: v3_ActCode_OptOutWithExceptions,
  /**
   * Prohibition on disclosure except as permitted by the information originator.
   */
  NoDisclosureWithoutOriginatorAuthorization: v3_ActCode_NoDisclosureWithoutOriginatorAuthorization,
  /**
   * A person reviews a list of orders submitted to a given patient.
   */
  OrdersReviewTask: v3_ActCode_OrdersReviewTask,
  /**
   * The service provided is required for orthodontic purposes. If the covered party has orthodontic coverage, then the service may be paid.
   */
  OrthodonticService: v3_ActCode_OrthodonticService,
  /**
   * Medicines designated in this way may be supplied for patient use without a prescription.  The exact form of categorisation will vary in different realms.
   */
  NonPrescriptionMedicine: v3_ActCode_NonPrescriptionMedicine,
  /**
   * Accommodations in which there is only 1 bed.
   */
  Private: v3_ActCode_Private,
  /**
   * Patient administration members who have access to healthcare consumer information as part of a patient administration workflows.
   *
   *                         Security Compartment Labels assigned to consumer information used in these workflows should be met or exceeded by the Security Compartment attribute claimed by a participant in a patient administration workflow who is requesting access to that information.
   */
  PatientAdministrationCompartment: v3_ActCode_PatientAdministrationCompartment,
  /**
   * Phenylketonuria diet.
   */
  PhenylalanineFree: v3_ActCode_PhenylalanineFree,
  /**
   * HealthCare facility preferred accommodation invoice.
   */
  PreferredAccommodationInvoice: v3_ActCode_PreferredAccommodationInvoice,
  /**
   * Provision of care for patients who are living or dying from an advanced illness.
   */
  Palliative: v3_ActCode_Palliative,
  /**
   * Paper documentation (or other physical format) with supporting or additional information to follow.
   */
  PaperDocumentationToFollow: v3_ActCode_PaperDocumentationToFollow,
  /**
   * Patient is supplied with parenteral nutrition, typically described in terms of i.v. medications.
   */
  Parenteral: v3_ActCode_Parenteral,
  /**
   * Communication of an agent from a living subject or environmental source to a living subject where the acquisition of the agent is not via the alimentary canal.
   */
  ParenteralTransmission: v3_ActCode_ParenteralTransmission,
  /**
   * Indicates that the ICSR is describing problems that a patient experienced after receiving a vaccine product.
   */
  PatientAdverseEvent: v3_ActCode_PatientAdverseEvent,
  /**
   * A person enters documentation about a given patient.
   */
  PatientDocumentationTask: v3_ActCode_PatientDocumentationTask,
  /**
   * A person provides a patient-specific education handout to a patient.
   */
  PatientEducationEntry: v3_ActCode_PatientEducationEntry,
  /**
   * A person (e.g., clinician, the patient herself) reviews patient information in the electronic medical record.
   */
  PatientInformationReviewTask: v3_ActCode_PatientInformationReviewTask,
  /**
   * Definition:The proposed therapy goes against preferences or consent constraints recorded in the patient's record.
   */
  ViolatesStatedPreferences: v3_ActCode_ViolatesStatedPreferences,
  /**
   * Definition:The proposed therapy goes against preferences or consent constraints recorded in the patient's record.  An alternate therapy meeting those constraints is available.
   */
  ViolatesStatedPreferencesAlternateAvailable: v3_ActCode_ViolatesStatedPreferencesAlternateAvailable,
  /**
   * A pathologist enters a report for a given patient.
   */
  PathologyReportEntryTask: v3_ActCode_PathologyReportEntryTask,
  /**
   * A person reviews a pathology report of a given patient.
   */
  PathologyReportReviewTask: v3_ActCode_PathologyReportReviewTask,
  /**
   * The guarantor, who may be the patient, pays the entire charge for a service. Reasons for such action may include: there is no insurance coverage for the service (e.g. cosmetic surgery); the patient wishes to self-pay for the service; or the insurer denies payment for the service due to contractual provisions such as the need for prior authorization.
   */
  Payment: v3_ActCode_Payment,
  /**
   * Transaction counts and value totals by each instance of an invoice payee.
   */
  Payee: v3_ActCode_Payee,
  /**
   * Transaction counts and value totals by each instance of an invoice payor.
   */
  Payor: v3_ActCode_Payor,
  /**
   * An account representing charges and credits (financial transactions) for a patient's encounter.
   */
  PatientBillingAccount: v3_ActCode_PatientBillingAccount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were paid prior to the specified time period (based on payment date), subsequently nullified in the specified period and submitted electronically.
   */
  PaidNullifiedPriorPeriodElectronicAmount: v3_ActCode_PaidNullifiedPriorPeriodElectronicAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were paid prior to the specified time period (based on payment date), subsequently nullified in the specified period and submitted electronically.
   */
  PaidNullifiedPriorPeriodElectronicCount: v3_ActCode_PaidNullifiedPriorPeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were paid prior to the specified time period (based on payment date), subsequently nullified in the specified period and submitted manually.
   */
  PaidNullifiedPriorPeriodManualAmount: v3_ActCode_PaidNullifiedPriorPeriodManualAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were paid prior to the specified time period (based on payment date), subsequently nullified in the specified period and submitted manually.
   */
  PaidNullifiedPriorPeriodManualCount: v3_ActCode_PaidNullifiedPriorPeriodManualCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were paid during the specified time period (based on payment date), subsequently nullified in the specified period and submitted electronically.
   */
  PaidNullifiedSamePeriodElectronicAmount: v3_ActCode_PaidNullifiedSamePeriodElectronicAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were paid during the specified time period (based on payment date), subsequently cancelled in the specified period and submitted electronically.
   */
  PaidNullifiedSamePeriodElectronicCount: v3_ActCode_PaidNullifiedSamePeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were paid during the specified time period (based on payment date), subsequently nullified in the specified period and submitted manually.
   */
  PaidNullifiedSamePeriodManualAmount: v3_ActCode_PaidNullifiedSamePeriodManualAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were paid during the specified time period (based on payment date), subsequently nullified in the specified period and submitted manually.
   */
  PaidNullifiedSamePeriodManualCount: v3_ActCode_PaidNullifiedSamePeriodManualCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were paid prior to the specified time period (based on payment date) that do not match a specified payee (e.g. pay patient) and submitted electronically.
   */
  PaidNonPayeePayablePriorPeriodElectronicAmount: v3_ActCode_PaidNonPayeePayablePriorPeriodElectronicAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were paid prior to the specified time period (based on payment date) that do not match a specified payee (e.g. pay patient) and submitted electronically.
   */
  PaidNonPayeePayablePriorPeriodElectronicCount: v3_ActCode_PaidNonPayeePayablePriorPeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were paid prior to the specified time period (based on payment date) that do not match a specified payee (e.g. pay patient) and submitted manually.
   */
  PaidNonPayeePayablePriorPeriodManualAmount: v3_ActCode_PaidNonPayeePayablePriorPeriodManualAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were paid prior to the specified time period (based on payment date) that do not match a specified payee (e.g. pay patient) and submitted manually.
   */
  PaidNonPayeePayablePriorPeriodManualCount: v3_ActCode_PaidNonPayeePayablePriorPeriodManualCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were paid during the specified time period (based on payment date) that do not match a specified payee (e.g. pay patient) and submitted electronically.
   */
  PaidNonPayeePayableSamePeriodElectronicAmount: v3_ActCode_PaidNonPayeePayableSamePeriodElectronicAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were paid during the specified time period (based on payment date) that do not match a specified payee (e.g. pay patient) and submitted electronically.
   */
  PaidNonPayeePayableSamePeriodElectronicCount: v3_ActCode_PaidNonPayeePayableSamePeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were paid during the specified time period (based on payment date) that do not match a specified payee (e.g. pay patient) and submitted manually.
   */
  PaidNonPayeePayableSamePeriodManualAmount: v3_ActCode_PaidNonPayeePayableSamePeriodManualAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were paid during the specified time period (based on payment date) that do not match a specified payee (e.g. pay patient) and submitted manually.
   */
  PaidNonPayeePayableSamePeriodManualCount: v3_ActCode_PaidNonPayeePayableSamePeriodManualCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were paid prior to the specified time period (based on payment date) that match a specified payee (e.g. pay provider) and submitted electronically.
   */
  PaidPayeePayablePriorPeriodElectronicAmount: v3_ActCode_PaidPayeePayablePriorPeriodElectronicAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were paid prior to the specified time period (based on payment date) that match a specified payee (e.g. pay provider) and submitted electronically.
   */
  PaidPayeePayablePriorPeriodElectronicCount: v3_ActCode_PaidPayeePayablePriorPeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were paid prior to the specified time period (based on payment date) that match a specified payee (e.g. pay provider) and submitted manually.
   */
  PaidPayeePayablePriorPeriodManualAmount: v3_ActCode_PaidPayeePayablePriorPeriodManualAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were paid prior to the specified time period (based on payment date) that match a specified payee (e.g. pay provider) and submitted manually.
   */
  PaidPayeePayablePriorPeriodManualCount: v3_ActCode_PaidPayeePayablePriorPeriodManualCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were paid during the specified time period (based on payment date) that match a specified payee (e.g. pay provider) and submitted electronically.
   */
  PaidPayeePayableSamePeriodElectronicAmount: v3_ActCode_PaidPayeePayableSamePeriodElectronicAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were paid during the specified time period (based on payment date) that match a specified payee (e.g. pay provider) and submitted electronically.
   */
  PaidPayeePayableSamePeriodElectronicCount: v3_ActCode_PaidPayeePayableSamePeriodElectronicCount,
  /**
   * Identifies the total net amount of all  Invoice Groupings that were paid during the specified time period (based on payment date) that match a specified payee (e.g. pay provider) and submitted manually.
   */
  PaidPayeePayableSamePeriodManualAmount: v3_ActCode_PaidPayeePayableSamePeriodManualAmount,
  /**
   * Identifies the total number of all  Invoice Groupings that were paid during the specified time period (based on payment date) that match a specified payee (e.g. pay provider) and submitted manually.
   */
  PaidPayeePayableSamePeriodManualCount: v3_ActCode_PaidPayeePayableSamePeriodManualCount,
  /**
   * Policy for specially protecting information reported by or about a patient, which is deemed sensitive within the enterprise (i.e., by default regardless of whether the patient requested that the information be deemed sensitive for another reason.) For example information reported by the patient about another person, e.g., a family member, may be deemed sensitive by default. Organizational policy may allow the sensitivity tag to be cleared on patient's request.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   *
   *                         For example, VA deems employee information sensitive by default.  Information about a patient who is being stalked or a victim of abuse or violence may be deemed sensitive by default per a provider organization's policies.
   */
  PatientDefaultInformationSensitivity: v3_ActCode_PatientDefaultInformationSensitivity,
  /**
   * Proposed therapy is outside of the standard practice for a pediatric patient.
   */
  PediatricAlert: v3_ActCode_PediatricAlert,
  /**
   * Provision of diagnosis and treatment of diseases and disorders affecting children.
   */
  Pediatrics: v3_ActCode_Pediatrics,
  /**
   * Anticipated or actual periodic fee associated with treating a patient. For example, expected billing cycle such as monthly, quarterly. The actual period (e.g. monthly, quarterly) is specified in the unit quantity of the Invoice Element.
   */
  PeriodicFee: v3_ActCode_PeriodicFee,
  /**
   * Transaction counts and value totals for the date range specified.
   */
  Period: v3_ActCode_Period,
  /**
   * The amount for a performance bonus that is being requested from a payor for the performance of certain services (childhood immunizations, influenza immunizations, mammograms, pap smears) on a sliding scale. That is, for 90% of childhood immunizations to a maximum of $2200/yr. An invoice is created at the end of the service period (one year) and a code is submitted indicating the percentage achieved and the dollar amount claimed.
   */
  PerformanceBonus: v3_ActCode_PerformanceBonus,
  /**
   * Custodian security system must persist the binding of security labels to classify information received or imported by information systems under its control for collection, access, use and disclosure in accordance with applicable jurisdictional privacy policies associated with the target information.  The system must retain an immutable record of the assignment and binding.
   */
  PersistSecurityLabel: v3_ActCode_PersistSecurityLabel,
  /**
   * Pharmaceutical care performed by a pharmacist.
   */
  Pharmaceutical: v3_ActCode_Pharmaceutical,
  /**
   * Policy for handling information about a patient, which a physician or other licensed healthcare provider deems sensitive.  Once tagged by the provider, this may trigger alerts for follow up actions according to organizational policy or jurisdictional law.
   *
   *
   *                            Usage Note: For use within an enterprise that provides heightened confidentiality to certain types of information designated by a physician as sensitive. If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   *
   *                         Use cases in which this code could be used are, e.g.,  in systems that lack the ability to automatically detect sensitive information and must rely on manual tagging; a system that lacks an applicable sensitivity tag, or for ad hoc situations where criticality of the situation requires that the tagging be done immediately by the provider before coding or transcription of consult notes can be completed, e.g., upon detection of a patient with suicidal tendencies or potential for violence.
   */
  PhysicianRequestedInformationSensitivity: v3_ActCode_PhysicianRequestedInformationSensitivity,
  /**
   * Provision of treatment for physical injury.
   */
  PhysicalRehab: v3_ActCode_PhysicalRehab,
  /**
   * Public Insurance has been exhausted.  Invoice has not been sent to Public Insuror and therefore no Explanation Of Benefits (EOB) is provided with this Invoice submission.
   */
  PublicInsuranceExhausted: v3_ActCode_PublicInsuranceExhausted,
  /**
   * Payment initiated by the payor as the result of adjudicating a paper (original, may have been faxed) invoice.
   */
  PaperInvoice: v3_ActCode_PaperInvoice,
  /**
   * Description: An interaction where the exposure participants were both present in the same location/place/space.
   */
  CommonSpaceInteraction: v3_ActCode_CommonSpaceInteraction,
  /**
   * Communication of an agent from a living subject to the progeny of that living subject via agent migration across the maternal-fetal placental membranes while in utero.
   */
  TransplacentalTransmission: v3_ActCode_TransplacentalTransmission,
  /**
   * A similar or identical therapy was recently ordered by a different practitioner.
   */
  PolyOrdererAlert: v3_ActCode_PolyOrdererAlert,
  /**
   * This patient was recently supplied a similar or identical therapy from a different pharmacy or supplier.
   */
  PolySupplierAlert: v3_ActCode_PolySupplierAlert,
  /**
   * Definition: A type of insurance that covers damage to or loss of the policyholderaTMs property by providing payments for damages to property damage or the injury or death of living subjects.  The terms "casualty" and "liability" insurance are often used interchangeably. Both cover the policyholder's legal liability for damages caused to other persons and/or their property.
   */
  PropertyAndCasualtyInsurancePolicy: v3_ActCode_PropertyAndCasualtyInsurancePolicy,
  /**
   * A single point denoted by a single (column,row) pair, or multiple points each denoted by a (column,row) pair.
   */
  Point: v3_ActCode_Point,
  /**
   * A series of connected line segments with ordered vertices denoted by (column,row) pairs; if the first and last vertices are the same, it is a closed polygon.
   */
  Polyline: v3_ActCode_Polyline,
  /**
   * Definition: A policy for a health plan that has features of both an HMO and a FFS plan.  Like an HMO, a POS plan encourages the use its HMO network to maintain discounted fees with participating providers, but recognizes that sometimes covered parties want to choose their own provider.  The POS plan allows a covered party to use providers who are not part of the HMO network (non-participating providers).  However, there is a greater cost associated with choosing these non-network providers. A covered party will usually pay deductibles and coinsurances that are substantially higher than the payments when he or she uses a plan provider. Use of non-participating providers often requires the covered party to pay the provider directly and then to file a claim for reimbursement, like in an FFS plan.
   */
  PointOfServicePolicy: v3_ActCode_PointOfServicePolicy,
  /**
   * Definition: A network-based, managed care plan that allows a covered party to choose any health care provider. However, if care is received from a "preferred" (participating in-network) provider, there are generally higher benefit coverage and lower deductibles.
   */
  PreferredProviderOrganizationPolicy: v3_ActCode_PreferredProviderOrganizationPolicy,
  /**
   * An amount that was owed to the payor as indicated, by a carry forward adjusment, in a previous payment advice
   */
  PriorPeriodAdjustment: v3_ActCode_PriorPeriodAdjustment,
  PRA: v3_ActCode_PRA,
  /**
   * A grouping of invoice element details including the one specifying the product (good or supply) being invoiced. It may also contain generic detail items such as tax or discount.
   */
  ProductInvoiceGroup: v3_ActCode_ProductInvoiceGroup,
  /**
   * Definition: Maximum amount paid by payer or covered party; or maximum number of services/products covered under the policy or program by time period specified by the effective time on the act.
   */
  PeriodMaximum: v3_ActCode_PeriodMaximum,
  /**
   * The dilution of the specimen made prior to being loaded onto analytical equipment
   */
  PreDilution: v3_ActCode_PreDilution,
  /**
   * An observation about how important a preference is to the target of the preference.
   */
  PreferenceStrength: v3_ActCode_PreferenceStrength,
  /**
   * Proposed therapy may be inappropriate or contraindicated during pregnancy
   */
  PregnancyAlert: v3_ActCode_PregnancyAlert,
  /**
   * A patient encounter where patient is scheduled or planned to receive service delivery in the future, and the patient is given a pre-admission account number. When the patient comes back for subsequent service, the pre-admission encounter is selected and is encapsulated into the service registration, and a new account number is generated.
   *
   *
   *                            Usage Note: This is intended to be used in advance of encounter types such as ambulatory, inpatient encounter, virtual, etc.
   */
  PreAdmission: v3_ActCode_PreAdmission,
  /**
   * Definition:The same or similar treatment has previously been attempted with the patient without achieving a positive effect.
   */
  PreviouslyIneffective: v3_ActCode_PreviouslyIneffective,
  /**
   * Custodian must create and/or maintain human readable security label tags as required by policy.
   *
   *                         Map:  Aligns with ISO 22600-3 Section A.3.4.3 description of privacy mark:  "If present, the privacy-mark is not used for access control. The content of the privacy-mark may be defined by the security policy in force (identified by the security-policy-identifier) which may define a list of values to be used. Alternately, the value may be determined by the originator of the security-label."
   */
  PrivacyMark: v3_ActCode_PrivacyMark,
  /**
   * Description:Indicates that a result is incomplete.  There are further results to come.  This maps to the 'active' state in the observation result status code.
   */
  Preliminary: v3_ActCode_Preliminary,
  /**
   * Definition:A list of medications which the patient will consume intermittently based on the behavior of the condition for which the medication is indicated.
   */
  AsNeeded: v3_ActCode_AsNeeded,
  /**
   * Professional association fee that is collected by the payor from the practitioner/provider on behalf of the association
   */
  ProfessionalAssociationDeduction: v3_ActCode_ProfessionalAssociationDeduction,
  /**
   * List of problem observations.
   */
  ProblemList: v3_ActCode_ProblemList,
  /**
   * A clinician enters a problem for a given patient.
   */
  ProblemListEntryTask: v3_ActCode_ProblemListEntryTask,
  /**
   * A person reviews a list of problems of a given patient.
   */
  ProblemListReviewTask: v3_ActCode_ProblemListReviewTask,
  /**
   * Transaction counts and value totals by Provider Identifier.
   */
  Provider: v3_ActCode_Provider,
  /**
   * Policy for specially protecting information reported by or about a patient, which the patient deems sensitive, and the patient requests that collection, access, use, or disclosure of that information be restricted.  For example, a minor patient may request that information about reproductive health not be disclosed to the patient's family or to particular providers and payers.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  PatientRequestedInformationSensitivity: v3_ActCode_PatientRequestedInformationSensitivity,
  /**
   * private transport
   */
  PrivateTransport: v3_ActCode_PrivateTransport,
  /**
   * Custodian system must strip information of data that would allow the identification of the source of the information or the information subject.  Custodian may retain a key to relink data necessary to reidentify the information subject.
   */
  Pseudonymize: v3_ActCode_Pseudonymize,
  /**
   * Tax levied by the provincial or state jurisdiction such as Provincial Sales Tax
   */
  ProvincialStateSalesTax: v3_ActCode_ProvincialStateSalesTax,
  /**
   * Definition:All information pertaining to a patient's professional service records (such as smoking cessation, counseling, medication review, mental health).
   */
  ProfessionalServiceCategory: v3_ActCode_ProfessionalServiceCategory,
  /**
   * Policy for handling psychiatry psychiatric disorder information, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  PsychiatryDisorderInformationSensitivity: v3_ActCode_PsychiatryDisorderInformationSensitivity,
  /**
   * Provision of treatment of psychiatric disorder relating to mental illness.
   */
  Psychiatric: v3_ActCode_Psychiatric,
  /**
   * Policy for handling psychotherapy note information, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: In some jurisdiction, disclosure of psychotherapy notes requires patient consent.
   *
   *                         If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  PsychotherapyNoteInformationSensitivity: v3_ActCode_PsychotherapyNoteInformationSensitivity,
  /**
   * Description: Exposure participants' interaction occurred during the course of  health care delivery by a provider (e.g. a physician treating a patient in her office).
   */
  HealthCareInteractionPatientCare: v3_ActCode_HealthCareInteractionPatientCare,
  /**
   * Insurance policy funded by a public health system such as a provincial or national health plan.  Examples include BC MSP (British Columbia 	Medical Services Plan) OHIP (Ontario Health Insurance Plan), NHS (National Health Service).
   */
  PublicHealthcare: v3_ActCode_PublicHealthcare,
  /**
   * public transport
   */
  PublicTransport: v3_ActCode_PublicTransport,
  /**
   * Allows provider to explain lateness of invoice to a subsequent payor.
   */
  DelayedByAPreviousPayor: v3_ActCode_DelayedByAPreviousPayor,
  /**
   * Status is used by one system to inform another that the processing has been completed, but the container has not been released from that system.
   */
  ProcessCompleted: v3_ActCode_ProcessCompleted,
  /**
   * Policy for handling information related to an information subject's race, which will be afforded heightened confidentiality.  Policies may govern sensitivity of information related to an information subject's race, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  RaceInformationSensitivity: v3_ActCode_RaceInformationSensitivity,
  /**
   * A radiologist enters a report for a given patient.
   */
  RadiologyReportEntryTask: v3_ActCode_RadiologyReportEntryTask,
  /**
   * A person reviews a radiology report of a given patient.
   */
  RadiologyReportReviewTask: v3_ActCode_RadiologyReportReviewTask,
  /**
   * Proposed therapy may be inappropriate or contraindicated because of a recorded patient allergy to a cross-sensitivity related product.  (Allergies are immune based reactions.)
   */
  RelatedAllergyAlert: v3_ActCode_RelatedAllergyAlert,
  /**
   * Proposed therapy may be inappropriate or contraindicated because of a recorded prior adverse reaction to a cross-sensitivity related product.
   */
  RelatedPriorReactionAlert: v3_ActCode_RelatedPriorReactionAlert,
  /**
   * Succinct statement of the need for the measure. Usually includes statements pertaining to Importance criterion: impact, gap in care and evidence.
   */
  Rationale: v3_ActCode_Rationale,
  /**
   * A diet that seeks to reduce body fat, typically low energy content (800-1600 kcal).
   */
  ReductionDiet: v3_ActCode_ReductionDiet,
  /**
   * Proposed therapy may be inappropriate or contraindicated based on the potential for a patient reaction to the proposed product
   */
  ReactionAlert: v3_ActCode_ReactionAlert,
  /**
   * The addition of calcium back to a specimen after it was removed by chelating agents
   */
  Recalcification: v3_ActCode_Recalcification,
  /**
   * Retroactive adjustment such as fee rate adjustment due to contract negotiations.
   */
  Recovery: v3_ActCode_Recovery,
  /**
   * Custodian system must remove information, which is not authorized to be access, used, or disclosed from records made available to otherwise authorized users.
   */
  Redact: v3_ActCode_Redact,
  /**
   * Identifies bibliographic citations or references to clinical practice guidelines, sources of evidence, or other relevant materials supporting the intent and rationale of the eMeasure.
   */
  Reference: v3_ActCode_Reference,
  /**
   * Specifies whether or not further testing may be automatically or manually initiated on specimens.
   */
  ReflexPermission: v3_ActCode_ReflexPermission,
  /**
   * Rules of practice do not require a physician's referral for the provider to perform a billable service.
   */
  ReferralNotRequired: v3_ActCode_ReferralNotRequired,
  /**
   * Conveys prohibited actions which an information custodian, receiver, or user is not permitted to perform unless otherwise authorized or permitted under specified circumstances.
   *
   *
   *
   *
   *                            Usage Notes: ISO 22600-2 species that a Refrain Policy "defines actions the subjects must refrain from performing".  Per HL7 Composite Security and Privacy Domain Analysis Model:  May be used to indicate that a specific action is prohibited based on specific access control attributes e.g., purpose of use, information type, user role, etc.
   */
  RefrainPolicy: v3_ActCode_RefrainPolicy,
  /**
   * Definition: An agreement between two or more insurance companies by which the risk of loss is proportioned. Thus the risk of loss is spread and a disproportionately large loss under a single policy does not fall on one insurance company. Acceptance by an insurer, called a reinsurer, of all or part of the risk of loss of another insurance company.
   *
   *
   *                            Discussion: Reinsurance is a means by which an insurance company can protect itself against the risk of losses with other insurance companies. Individuals and corporations obtain insurance policies to provide protection for various risks (hurricanes, earthquakes, lawsuits, collisions, sickness and death, etc.). Reinsurers, in turn, provide insurance to insurance companies.
   *
   *                         For example, an HMO may purchase a reinsurance policy to protect itself from losing too much money from one insured's particularly expensive health care costs. An insurance company issuing an automobile liability policy, with a limit of $100,000 per accident may reinsure its liability in excess of $10,000. A fire insurance company which issues a large policy generally reinsures a portion of the risk with one or several other companies. Also called risk control insurance or stop-loss insurance.
   */
  ReinsurancePolicy: v3_ActCode_ReinsurancePolicy,
  /**
   * Policy for handling information related to an information subject's religious affiliation, which will be afforded heightened confidentiality.  Policies may govern sensitivity of information related to an information subject's religion, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Notes: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  ReligionInformationSensitivity: v3_ActCode_ReligionInformationSensitivity,
  /**
   * Description: A person enters a health care reminder for a given patient.
   */
  ReminderListEntry: v3_ActCode_ReminderListEntry,
  /**
   * Description: A person reviews a list of health care reminders for a given patient.
   */
  ReminderListReview: v3_ActCode_ReminderListReview,
  /**
   * Temporary supply of a product with financial compensation, without transfer of ownership for the product.
   */
  Rent: v3_ActCode_Rent,
  /**
   * Description:This observation represents an 'average' or 'expected' half-life typical of the product.
   */
  RepresentativeHalfLife: v3_ActCode_RepresentativeHalfLife,
  /**
   * Description:The number of repeating elements falls outside the range of the allowed number of repetitions.
   */
  RepetitionsOutOfRange: v3_ActCode_RepetitionsOutOfRange,
  /**
   * This Observation Series type contains waveforms of a "representative beat" (a.k.a. "median beat" or "average beat").  The waveform samples are measured in relative time, relative to the beginning of the beat as defined by the Observation Series effective time.  The waveforms are not directly acquired from the subject, but rather algorithmically derived from the "rhythm" waveforms.
   */
  ECGRepresentativeBeatWaveforms: v3_ActCode_ECGRepresentativeBeatWaveforms,
  /**
   * The same service was delivered within a time period that would usually indicate a duplicate billing.  However, the repeated service is a medical 	necessity and therefore not a duplicate.
   */
  RepeatedService: v3_ActCode_RepeatedService,
  /**
   * The value of the dilution of a sample after it had been analyzed at a prior dilution value
   */
  RerunDilution: v3_ActCode_RerunDilution,
  /**
   * A security category label field value, which indicates that access and use of an IT resource is restricted to members of a research project.
   */
  ResearchProjectCompartment: v3_ActCode_ResearchProjectCompartment,
  /**
   * Definition: Consent to have healthcare information in an electronic health record accessed for research purposes.
   */
  ResearchInformationAccess: v3_ActCode_ResearchInformationAccess,
  /**
   * A charge is requested because the patient failed to pick up the item and it took an amount of time to return it to stock for future use.
   */
  RestockingFee: v3_ActCode_RestockingFee,
  /**
   * Definition: A government mandated program with specific eligibility requirements based on premium contributions made during employment, length of employment, age, and employment status, e.g., being retired, disabled, or a dependent of a covered party under this program.   Benefits typically include ambulatory, inpatient, and long-term care, such as hospice care, home health care and respite care.
   */
  RetireeHealthProgram: v3_ActCode_RetireeHealthProgram,
  /**
   * Bonus payments based on performance, volume, etc. as agreed to by the payor.
   */
  RetroAdjustment: v3_ActCode_RetroAdjustment,
  /**
   * A type of transaction that represents a reversal of a previous charge for a service or product. Expressed in monetary terms.  It has the opposite effect of a standard charge.
   */
  StandardChargeReversal: v3_ActCode_StandardChargeReversal,
  /**
   * A fill against an order that has already been filled (or partially filled) at least once.
   */
  Refill: v3_ActCode_Refill,
  /**
   * A refill where the quantity supplied is equal to one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a complete fill would be for the full 90 tablets.)
   */
  RefillComplete: v3_ActCode_RefillComplete,
  /**
   * A refill where the quantity supplied is equal to one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a complete fill would be for the full 90 tablets.) and where the strength supplied is less than the ordered strength (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  RefillCompletePartialStrength: v3_ActCode_RefillCompletePartialStrength,
  /**
   * The first fill against an order that has already been filled at least once at another facility.
   */
  RefillFirstFillThisFacility: v3_ActCode_RefillFirstFillThisFacility,
  /**
   * The first fill against an order that has already been filled at least once at another facility and where the strength supplied is less than the ordered strength (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  RefillPartialStrengthFirstFillThisFacility: v3_ActCode_RefillPartialStrengthFirstFillThisFacility,
  /**
   * A refill where the quantity supplied is less than one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a partial fill might be for only 30 tablets.)
   */
  RefillPartFill: v3_ActCode_RefillPartFill,
  /**
   * A refill where the quantity supplied is less than one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a partial fill might be for only 30 tablets.) and where the strength supplied is less than the ordered strength (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  RefillPartFillPartialStrength: v3_ActCode_RefillPartFillPartialStrength,
  /**
   * A fill against an order that has already been filled (or partially filled) at least once and where the strength supplied is less than the ordered strength (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  RefillPartialStrength: v3_ActCode_RefillPartialStrength,
  /**
   * This Observation type contains ECG "rhythm" waveforms.  The waveform samples are measured in absolute time (a.k.a. "subject time" or "effective time").  These waveforms are usually "raw" with some minimal amount of noise reduction and baseline filtering applied.
   */
  ECGRhythmWaveforms: v3_ActCode_ECGRhythmWaveforms,
  /**
   * Proposed therapy may be inappropriate or contraindicated because of a recorded patient intolerance to a cross-sensitivity related product.  (Intolerances are non-immune based sensitivities.)
   */
  RelatedIntoleranceAlert: v3_ActCode_RelatedIntoleranceAlert,
  /**
   * A person reviews a Risk Assessment Instrument report of a given patient.
   */
  RiskAssessmentInstrumentTask: v3_ActCode_RiskAssessmentInstrumentTask,
  /**
   * List of risk factor observations.
   */
  RiskFactors: v3_ActCode_RiskFactors,
  /**
   * A security category label field value, which indicates that access and use of an IT resource is restricted to members of records management department or workflow.
   */
  RecordsManagementCompartment: v3_ActCode_RecordsManagementCompartment,
  /**
   * A fully specified bounded Region of Interest (ROI) delineates a ROI in which only those dimensions participate that are specified by boundary criteria, whereas all other dimensions are excluded.  For example a ROI to mark an episode of "ST elevation" in a subset of the EKG leads V2, V3, and V4 would include 4 boundaries, one each for time, V2, V3, and V4.
   */
  FullySpecifiedROI: v3_ActCode_FullySpecifiedROI,
  /**
   * A partially specified bounded Region of Interest (ROI) specifies a ROI in which at least all values in the dimensions specified by the boundary criteria participate. For example, if an episode of ventricular fibrillations (VFib) is observed, it usually doesn't make sense to exclude any EKG leads from the observation and the partially specified ROI would contain only one boundary for time indicating the time interval where VFib was observed.
   */
  PartiallySpecifiedROI: v3_ActCode_PartiallySpecifiedROI,
  /**
   * A billing arrangement where funding is based on a list of individuals registered as patients of the Provider.
   */
  RosterFunding: v3_ActCode_RosterFunding,
  /**
   * Proposed therapy may be inappropriate or contraindicated because of a potential patient reaction to a cross-sensitivity related product.
   */
  RelatedReactionAlert: v3_ActCode_RelatedReactionAlert,
  /**
   * Definition: Consent to have de-identified healthcare information in an electronic health record that is accessed for research purposes, but without consent to re-identify the information under any circumstance.
   */
  DeIdentifiedInformationAccess: v3_ActCode_DeIdentifiedInformationAccess,
  /**
   * Definition: Consent to have de-identified healthcare information in an electronic health record that is accessed for research purposes re-identified under specific circumstances outlined in the consent.
   *
   *
   *                            Example:: Where there is a need to inform the subject of potential health issues.
   */
  ReIdentifiableInformationAccess: v3_ActCode_ReIdentifiableInformationAccess,
  /**
   * Some form of prescription is required before the related medicine can be supplied for a patient.  The exact form of regulation will vary in different realms.
   */
  PrescriptionOnlyMedicine: v3_ActCode_PrescriptionOnlyMedicine,
  /**
   * Definition:All information pertaining to a patient's medication records (orders, dispenses and other active medications).
   */
  MedicationCategory: v3_ActCode_MedicationCategory,
  /**
   * Pharmacy dispense invoice for a compound.
   */
  RxCompoundInvoice: v3_ActCode_RxCompoundInvoice,
  /**
   * Pharmacy dispense invoice not involving a compound
   */
  RxDispenseInvoice: v3_ActCode_RxDispenseInvoice,
  /**
   * Uniquely designed and elegantly decorated accommodations with many amenities available for an additional charge.
   */
  Suite: v3_ActCode_Suite,
  /**
   * Definition:A drug that requires prior approval (to be reimbursed) before being dispensed
   */
  SpecialAuthorization: v3_ActCode_SpecialAuthorization,
  /**
   * Description:A drug that requires special access permission to be prescribed and dispensed.
   */
  SpecialAccess: v3_ActCode_SpecialAccess,
  /**
   * Definition: Government administered and funded program to support provision of care to underserved populations through safety net clinics.
   *
   *
   *                            Example: In the U.S., safety net providers such as federally qualified health centers (FQHC) receive funding under PHSA Section 330 grants administered by the Health Resources and Services Administration.
   */
  SafetyNetClinicProgram: v3_ActCode_SafetyNetClinicProgram,
  /**
   * Transfer of ownership for a product for financial compensation.
   */
  Sale: v3_ActCode_Sale,
  /**
   * Identifies the total net amount billed for all submitted Invoice Groupings within a time period and submitted electronically.  Adjudicated invoice elements are included.
   */
  SubmittedBilledElectronicAmount: v3_ActCode_SubmittedBilledElectronicAmount,
  /**
   * Identifies the total number of submitted Invoice Groupings within a time period and submitted electronically.  Adjudicated invoice elements are included.
   */
  SubmittedBilledElectronicCount: v3_ActCode_SubmittedBilledElectronicCount,
  /**
   * Clinical services invoice where the Invoice Group contains one billable item for multiple clinical services in one or more sessions.
   */
  SessionalOrBlockFeeInvoice: v3_ActCode_SessionalOrBlockFeeInvoice,
  /**
   * Identifies the total net amount billed for all submitted  Invoice Groupings that were nullified within a time period and submitted electronically.  Adjudicated invoice elements are included.
   */
  SubmittedNullifiedElectronicAmount: v3_ActCode_SubmittedNullifiedElectronicAmount,
  /**
   * Identifies the total number of submitted  Invoice Groupings that were nullified within a time period and submitted electronically.  Adjudicated invoice elements are included.
   */
  SubmittedCancelledElectronicCount: v3_ActCode_SubmittedCancelledElectronicCount,
  /**
   * Identifies the total net amount billed for all submitted  Invoice Groupings that are pended or held by the payor, within a time period and submitted electronically.  Adjudicated invoice elements are not included.
   */
  SubmittedPendingElectronicAmount: v3_ActCode_SubmittedPendingElectronicAmount,
  /**
   * Identifies the total number of submitted  Invoice Groupings that are pended or held by the payor, within a time period and submitted electronically.  Adjudicated invoice elements are not included.
   */
  SubmittedPendingElectronicCount: v3_ActCode_SubmittedPendingElectronicCount,
  /**
   * Policy for handling sickle cell disease information, which is afforded heightened confidentiality.  Information handling protocols are based on organizational policies related to sickle cell disease information, which is deemed sensitive.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then the Act valued with this ActCode should be associated with an Act valued with any applicable laws from the ActPrivacyLaw code system.
   */
  SickleCellAnemiaInformationSensitivity: v3_ActCode_SickleCellAnemiaInformationSensitivity,
  /**
   * A diet that avoids ingredients that might cause digestion problems, e.g., avoid excessive fat, avoid too much fiber (cabbage, peas, beans).
   */
  SchonkostGE: v3_ActCode_SchonkostGE,
  /**
   * Description: The school the patient attended when immunized.
   */
  School: v3_ActCode_School,
  /**
   * Description: The school division or district associated with the patient during the immunization event.
   */
  SchoolDivision: v3_ActCode_SchoolDivision,
  /**
   * Incident or accident is the result of a school place accident.
   */
  SchoolAccident: v3_ActCode_SchoolAccident,
  /**
   * Description: Exposure participants' interaction occurred in an academic setting (e.g., participants are fellow students, or student and teacher).
   */
  SchoolInteraction: v3_ActCode_SchoolInteraction,
  /**
   * Comparison of results across strata can be used to show where disparities exist or where there is a need to expose differences in results. For example, Centers for Medicare & Medicaid Services (CMS) in the U.S. defines four required Supplemental Data Elements (payer, ethnicity, race, and gender), which are variables used to aggregate data into various subgroups. Additional supplemental data elements required for risk adjustment or other purposes of data aggregation can be included in the Supplemental Data Element section.
   */
  SupplementalDataElements: v3_ActCode_SupplementalDataElements,
  /**
   * Policy for handling sexual assault, abuse, or domestic violence information, which will be afforded heightened confidentiality. Information handling protocols based on organizational policies related to sexual assault, abuse, or domestic violence information that is deemed sensitive.
   *
   *                         SDV code covers violence perpetrated by related and non-related persons. This code should be specific to physical and mental trauma caused by a related person only.  The access control concerns are keeping the patient safe from the perpetrator who may have an abusive psychological control over the patient, may be stalking the patient, or may try to manipulate care givers into allowing the perpetrator to make contact with the patient.  The definition needs to be clarified.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  SexualAssaultAbuseOrDomesticViolenceInformationSensitivity: v3_ActCode_SexualAssaultAbuseOrDomesticViolenceInformationSensitivity,
  /**
   * Type of security metadata observation made about the alteration integrity of an IT resource (data, information object, service, or system capability), which indicates the mechanism used for authorized transformations of the resource.
   *
   *
   *                            Examples: Types of security alteration integrity observation metadata, which may value the observation with a code used to indicate the mechanism used for authorized transformation of an IT resource, including:
   *
   *
   *                            translation
   *                            syntactic transformation
   *                            semantic mapping
   *                            redaction
   *                            masking
   *                            pseudonymization
   *                            anonymization
   */
  SecurityAlterationIntegrityObservation: v3_ActCode_SecurityAlterationIntegrityObservation,
  /**
   * Type of security metadata observation made about the category of an IT resource (data, information object, service, or system capability), which may be used to make access control decisions. Security category metadata is defined by ISO/IEC 2382-8:1998(E/F)/ T-REC-X.812-1995 as: "A nonhierarchical grouping of sensitive information used to control access to data more finely than with hierarchical security classification alone."
   *
   *
   *                            Rationale: A security category observation supports requirement to specify the type of IT resource to facilitate application of appropriate levels of information security according to a range of levels of impact or consequences that might result from the unauthorized disclosure, modification, or use of the information or information system.  A resource is assigned to a specific category of information (e.g., privacy, medical, proprietary, financial, investigative, contractor sensitive, security management) defined by an organization or in some instances, by a specific law, Executive Order, directive, policy, or regulation. [FIPS 199]
   *
   *
   *                            Examples: Types of security categories include:
   *
   *
   *                            Compartment:  A division of data into isolated blocks with separate security controls for the purpose of reducing risk. (ISO 2382-8).  A security label tag that "segments" an IT resource by indicating that access and use is restricted to members of a defined community or project. (HL7 Healthcare Classification System)
   *                            Sensitivity:  The characteristic of an IT resource which implies its value or importance and may include its vulnerability. (ISO 7492-2)  Privacy metadata for information perceived as undesirable to share.  (HL7 Healthcare Classification System)
   */
  SecurityCategoryObservation: v3_ActCode_SecurityCategoryObservation,
  /**
   * Type of security metadata observation made about the classification of an IT resource (data, information object, service, or system capability), which may be used to make access control decisions.  Security classification is defined by ISO/IEC 2382-8:1998(E/F)/ T-REC-X.812-1995 as: "The determination of which specific degree of protection against access the data or information requires, together with a designation of that degree of protection."  Security classification metadata is based on an analysis of applicable policies and the risk of financial, reputational, or other harm that could result from unauthorized disclosure.
   *
   *
   *                            Rationale: A security classification observation may indicate that the confidentiality level indicated by an Act or Role confidentiality attribute has been overridden by the entity responsible for ascribing the SecurityClassificationObservationValue.  This supports the business requirement for increasing or decreasing the level of confidentiality (classification or declassification) based on parameters beyond the original assignment of an Act or Role confidentiality.
   *
   *
   *                            Examples: Types of security classification include: HL7 Confidentiality Codes such as very restricted, unrestricted, and normal.  Intelligence community examples include top secret, secret, and confidential.
   *
   *
   *                            Usage Note: Security classification observation type codes designate security label field types, which are valued with an applicable SecurityClassificationObservationValue code as the "security label tag".
   */
  SecurityClassificationObservation: v3_ActCode_SecurityClassificationObservation,
  /**
   * Type of security metadata observation made about the control of an IT resource (data, information object, service, or system capability), which may be used to make access control decisions.  Security control metadata convey instructions to users and receivers for secure distribution, transmission, and storage; dictate obligations or mandated actions; specify any action prohibited by refrain policy such as dissemination controls; and stipulate the permissible purpose of use of an IT resource.
   *
   *
   *                            Rationale: A security control observation supports requirement to specify applicable management, operational, and technical controls (i.e., safeguards or countermeasures) prescribed for an information system to protect the confidentiality, integrity, and availability of the system and its information. [FIPS 199]
   *
   *
   *                            Examples: Types of security control metadata include:
   *
   *
   *                            handling caveats
   *                            dissemination controls
   *                            obligations
   *                            refrain policies
   *                            purpose of use constraints
   */
  SecurityControlObservation: v3_ActCode_SecurityControlObservation,
  /**
   * Type of security metadata observation made about the data integrity of an IT resource (data, information object, service, or system capability), which indicates the security mechanism used to preserve resource accuracy and consistency.  Data integrity is defined by ISO 22600-23.3.21 as: "The property that data has not been altered or destroyed in an unauthorized manner", and by ISO/IEC 2382-8:  The property of data whose accuracy and consistency are preserved regardless of changes made."
   *
   *
   *                            Examples: Types of security data integrity observation metadata, which may value the observation, include cryptographic hash function and digital signature.
   */
  SecurityDataIntegrityObservation: v3_ActCode_SecurityDataIntegrityObservation,
  /**
   * Type of security metadata observation made about the integrity confidence of an IT resource (data, information object, service, or system capability), which may be used to make access control decisions.
   *
   *
   *                            Examples: Types of security integrity confidence observation metadata, which may value the observation, include highly reliable, uncertain reliability, and not reliable.
   *
   *
   *                            Usage Note: A security integrity confidence observation on an Act may indicate that a valued Act.uncertaintycode attribute has been overridden by the entity responsible for ascribing the SecurityIntegrityConfidenceObservationValue.  This supports the business requirements for increasing or decreasing the assessment of the reliability or trustworthiness of an IT resource based on parameters beyond the original assignment of an Act statement level of uncertainty.
   */
  SecurityIntegrityConfidenceObservation: v3_ActCode_SecurityIntegrityConfidenceObservation,
  /**
   * Type of security metadata observation made about the integrity of an IT resource (data, information object, service, or system capability), which may be used to make access control decisions.
   *
   *
   *                            Rationale: A security integrity observation supports the requirement to guard against improper information modification or destruction, and includes ensuring information non-repudiation and authenticity. (44 U.S.C., SEC. 3542)
   *
   *
   *                            Examples: Types of security integrity metadata include:
   *
   *
   *                            Integrity status, which indicates the completeness or workflow status of an IT resource (data, information object, service, or system capability)
   *                            Integrity confidence, which indicates the reliability and trustworthiness of an IT resource
   *                            Integrity control, which indicates pertinent handling caveats, obligations, refrain policies, and purpose of use for  the resource
   *                            Data integrity, which indicate the security mechanisms used to ensure that the accuracy and consistency are preserved regardless of changes made (ISO/IEC DIS 2382-8)
   *                            Alteration integrity, which indicate the security mechanisms used for authorized transformations of the resource
   *                            Integrity provenance, which indicates the entity responsible for a report or assertion relayed "second-hand" about an IT resource
   */
  SecurityIntegrityObservation: v3_ActCode_SecurityIntegrityObservation,
  /**
   * Type of security metadata observation made about the integrity provenance of an IT resource (data, information object, service, or system capability), which indicates the entity that made assertions about the resource.  The asserting entity may not be the original informant about the resource.
   *
   *
   *                            Examples: Types of security integrity provenance asserted by observation metadata, which may value the observation, including:
   *
   *
   *                            assertions about an IT resource by a patient
   *                            assertions about an IT resource by a clinician
   *                            assertions about an IT resource by a device
   */
  SecurityIntegrityProvenanceAssertedByObservation: v3_ActCode_SecurityIntegrityProvenanceAssertedByObservation,
  /**
   * Type of security metadata observation made about the provenance integrity of an IT resource (data, information object, service, or system capability), which indicates the lifecycle completeness of an IT resource in terms of workflow status such as its creation, modification, suspension, and deletion; locations in which the resource has been collected or archived, from which it may be retrieved, and the history of its distribution and disclosure.  Integrity provenance metadata about an IT resource may be used to assess its veracity, reliability, and trustworthiness.
   *
   *
   *                            Examples: Types of security integrity provenance observation metadata, which may value the observation about an IT resource, include:
   *
   *
   *                            completeness or workflow status, such as authentication
   *                            the entity responsible for original authoring or informing about an IT resource
   *                            the entity responsible for a report or assertion about an IT resource relayed â€œsecond-handâ€?
   *                            the entity responsible for excerpting, transforming, or compiling an IT resource
   */
  SecurityIntegrityProvenanceObservation: v3_ActCode_SecurityIntegrityProvenanceObservation,
  /**
   * Type of security metadata observation made about the integrity provenance of an IT resource (data, information object, service, or system capability), which indicates the entity that reported the existence of the resource.  The reporting entity may not be the original author of the resource.
   *
   *
   *                            Examples: Types of security integrity provenance reported by observation metadata, which may value the observation, include:
   *
   *
   *                            reports about an IT resource by a patient
   *                            reports about an IT resource by a clinician
   *                            reports about an IT resource by a device
   */
  SecurityIntegrityProvenanceReportedByObservation: v3_ActCode_SecurityIntegrityProvenanceReportedByObservation,
  /**
   * Type of security metadata observation made about the integrity status of an IT resource (data, information object, service, or system capability), which may be used to make access control decisions.  Indicates the completeness of an IT resource in terms of workflow status, which may impact users that are authorized to access and use the resource.
   *
   *
   *                            Examples: Types of security integrity status observation metadata, which may value the observation, include codes from the HL7 DocumentCompletion code system such as legally authenticated, in progress, and incomplete.
   */
  SecurityIntegrityStatusObservation: v3_ActCode_SecurityIntegrityStatusObservation,
  /**
   * An observation identifying security metadata about an IT resource (data, information object, service, or system capability), which may be used to make access control decisions.  Security metadata are used to name security labels.
   *
   *
   *                            Rationale: According to ISO/TS 22600-3:2009(E) A.9.1.7 SECURITY LABEL MATCHING, Security label matching compares the initiator's clearance to the target's security label.  All of the following must be true for authorization to be granted:
   *
   *
   *                            The security policy identifiers shall be identical
   *                            The classification level of the initiator shall be greater than or equal to that of the target (that is, there shall be at least one value in the classification list of the clearance greater than or equal to the classification of the target), and
   *                            For each security category in the target label, there shall be a security category of the same type in the initiator's clearance and the initiator's classification level shall dominate that of the target.
   *
   *
   *                            Examples: SecurityObservationType  security label fields include:
   *
   *
   *                            Confidentiality classification
   *                            Compartment category
   *                            Sensitivity category
   *                            Security mechanisms used to ensure data integrity or to perform authorized data transformation
   *                            Indicators of an IT resource completeness, veracity, reliability, trustworthiness, or provenance.
   *
   *
   *                            Usage Note: SecurityObservationType codes designate security label field types, which are valued with an applicable SecurityObservationValue code as the "security label tag".
   */
  SecurityObservationType: v3_ActCode_SecurityObservationType,
  /**
   * An observation identifying trust metadata about an IT resource (data, information object, service, or system capability), which may be used as a trust attribute to populate a computable trust policy, trust credential, trust assertion, or trust label field in a security label or trust policy, which are principally used for authentication, authorization, and access control decisions.
   */
  SECTRSTOBS: v3_ActCode_SECTRSTOBS,
  /**
   * Types of security policies that further specify the ActClassPolicy value set.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            obligation to encrypt
   *                            refrain from redisclosure without consent
   */
  SecurityPolicy: v3_ActCode_SecurityPolicy,
  /**
   * Transaction counts and value totals by each instance of a messaging application on a single processor. It is a registered identifier known to the receivers.
   */
  SendingApplication: v3_ActCode_SendingApplication,
  /**
   * A billing arrangement where a Provider charges a sum to provide a group (volume) of interventions/procedures to one or more patients within a defined period of time, typically on the same date.  Interventions/procedures included in the session may vary.
   */
  SessionalFunding: v3_ActCode_SessionalFunding,
  /**
   * A subjective evaluation of the seriousness or intensity associated with another observation.
   */
  SeverityObservation: v3_ActCode_SeverityObservation,
  /**
   * Policy for handling sexuality and reproductive health information, which will be afforded heightened confidentiality.  Information handling protocols based on organizational policies related to sexuality and reproductive health information that is deemed sensitive.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  SexualityAndReproductiveHealthInformationSensitivity: v3_ActCode_SexualityAndReproductiveHealthInformationSensitivity,
  /**
   * Communication of an agent from one living subject to another living subject through direct contact with genital or oral tissues as part of a sexual act.
   */
  SexualTransmission: v3_ActCode_SexualTransmission,
  /**
   * Types of sensitivity policies that apply to Acts.  Act.confidentialityCode is defined in the RIM as "constraints around appropriate disclosure of information about this Act, regardless of mood."
   *
   *
   *                            Usage Note: ActSensitivity codes are used to bind information to an Act.confidentialityCode according to local sensitivity policy so that those confidentiality codes can then govern its handling across enterprises.  Internally to a policy domain, however, local policies guide the access control system on how end users in that policy domain are able to use information tagged with these sensitivity values.
   */
  SickleCell: v3_ActCode_SickleCell,
  /**
   * An emergency supply where the expectation is that a formal order authorizing the supply will be provided at a later date.
   */
  ScriptOwing: v3_ActCode_ScriptOwing,
  /**
   * Definition: A social service program funded by a public or governmental entity.
   *
   *
   *                            Example: Programs providing habilitation, food, lodging, medicine, transportation, equipment, devices, products, education, training, counseling, alteration of living or work space, and other resources to persons meeting eligibility criteria.
   */
  SocialServiceProgram: v3_ActCode_SocialServiceProgram,
  /**
   * Description: An interaction where the exposure participants are social associates or members of the same extended family
   */
  SocialExtendedFamilyInteraction: v3_ActCode_SocialExtendedFamilyInteraction,
  /**
   * Accommodations in which there are 2 beds.
   */
  SemiPrivate: v3_ActCode_SemiPrivate,
  /**
   * That total amount of the eligible charges which a covered party must periodically pay for services and/or products prior to the Medicaid program providing any coverage. This amount represents the covered party's spend down that is applied to a particular adjudication result. It is expressed as a negative dollar amount in adjudication results
   */
  SpendDown: v3_ActCode_SpendDown,
  /**
   * Policy for handling information deemed specially protected by law or policy including substance abuse, substance use, psychiatric, mental health, behavioral health, and cognitive disorders, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  SpeciallyProtectedInformationSensitivity: v3_ActCode_SpeciallyProtectedInformationSensitivity,
  /**
   * Definition: A characteristic of an oral solid dosage form of a medicinal product, indicating whether it has one or more coatings such as sugar coating, film coating, or enteric coating.  Only coatings to the external surface or the dosage form should be considered (for example, coatings to individual pellets or granules inside a capsule or tablet are excluded from consideration).
   *
   *
   *                            Constraints: The Observation.value must be a Boolean (BL) with true for the presence or false for the absence of one or more coatings on a solid dosage form.
   */
  Coating_2: v3_ActCode_Coating_2,
  /**
   * Definition:  A characteristic of an oral solid dosage form of a medicinal product, specifying the color or colors that most predominantly define the appearance of the dose form. SPLCOLOR is not an FDA specification for the actual color of solid dosage forms or the names of colors that can appear in labeling.
   *
   *
   *                            Constraints: The Observation.value must be a single coded value or a list of multiple coded values, specifying one or more distinct colors that approximate of the color(s) of distinct areas of the solid dosage form, such as the different sides of a tablet or one-part capsule, or the different halves of a two-part capsule.  Bands on banded capsules, regardless of the color, are not considered when assigning an SPLCOLOR. Imprints on the dosage form, regardless of their color are not considered when assigning an SPLCOLOR. If more than one color exists on a particular side or half, then the most predominant color on that side or half is recorded.  If the gelatin capsule shell is colorless and transparent, use the predominant color of the contents that appears through the colorless and transparent capsule shell. Colors can include: Black;Gray;White;Red;Pink;Purple;Green;Yellow;Orange;Brown;Blue;Turquoise.
   */
  Color_2: v3_ActCode_Color_2,
  /**
   * Description: A characteristic representing a single file reference that contains two or more views of the same dosage form of the product; in most cases this should represent front and back views of the dosage form, but occasionally additional views might be needed in order to capture all of the important physical characteristics of the dosage form.  Any imprint and/or symbol should be clearly identifiable, and the viewer should not normally need to rotate the image in order to read it.  Images that are submitted with SPL should be included in the same directory as the SPL file.
   */
  Image: v3_ActCode_Image,
  /**
   * Definition:  A characteristic of an oral solid dosage form of a medicinal product, specifying the alphanumeric text that appears on the solid dosage form, including text that is embossed, debossed, engraved or printed with ink. The presence of other non-textual distinguishing marks or symbols is recorded by SPLSYMBOL.
   *
   *
   *                            Examples: Included in SPLIMPRINT are alphanumeric text that appears on the bands of banded capsules and logos and other symbols that can be interpreted as letters or numbers.
   *
   *
   *                            Constraints: The Observation.value must be of type Character String (ST). Excluded from SPLIMPRINT are internal and external cut-outs in the form of alphanumeric text and the letter 'R' with a circle around it (when referring to a registered trademark) and the letters 'TM' (when referring to a 'trade mark').  To record text, begin on either side or part of the dosage form. Start at the top left and progress as one would normally read a book.  Enter a semicolon to show separation between words or line divisions.
   */
  Imprint: v3_ActCode_Imprint,
  /**
   * Definition: A characteristic of an oral solid dosage form of a medicinal product, specifying the number of equal pieces that the solid dosage form can be divided into using score line(s).
   *
   *
   *                            Example: One score line creating two equal pieces is given a value of 2, two parallel score lines creating three equal pieces is given a value of 3.
   *
   *
   *                            Constraints: Whether three parallel score lines create four equal pieces or two intersecting score lines create two equal pieces using one score line and four equal pieces using both score lines, both have the scoring value of 4. Solid dosage forms that are not scored are given a value of 1. Solid dosage forms that can only be divided into unequal pieces are given a null-value with nullFlavor other (OTH).
   */
  Scoring_2: v3_ActCode_Scoring_2,
  /**
   * Description: A characteristic of an oral solid dosage form of a medicinal product, specifying the two dimensional representation of the solid dose form, in terms of the outside perimeter of a solid dosage form when the dosage form, resting on a flat surface, is viewed from directly above, including slight rounding of corners. SPLSHAPE does not include embossing, scoring, debossing, or internal cut-outs.  SPLSHAPE is independent of the orientation of the imprint and logo. Shapes can include: Triangle (3 sided); Square; Round; Semicircle; Pentagon (5 sided); Diamond; Double circle; Bullet; Hexagon (6 sided); Rectangle; Gear; Capsule; Heptagon (7 sided); Trapezoid; Oval; Clover; Octagon (8 sided); Tear; Freeform.
   */
  Shape_2: v3_ActCode_Shape_2,
  /**
   * Definition: A characteristic of an oral solid dosage form of a medicinal product, specifying the longest single dimension of the solid dosage form as a physical quantity in the dimension of length (e.g., 3 mm). The length is should be specified in millimeters and should be rounded to the nearest whole millimeter.
   *
   *
   *                            Example: SPLSIZE for a rectangular shaped tablet is the length and SPLSIZE for a round shaped tablet is the diameter.
   */
  Size_2: v3_ActCode_Size_2,
  /**
   * Definition: A characteristic of an oral solid dosage form of a medicinal product, to describe whether or not the medicinal product has a mark or symbol appearing on it for easy and definite recognition.  Score lines, letters, numbers, and internal and external cut-outs are not considered marks or symbols. See SPLSCORING and SPLIMPRINT for these characteristics.
   *
   *
   *                            Constraints: The Observation.value must be a Boolean (BL) with <u>true</u> indicating the presence and <u>false</u> for the absence of marks or symbols.
   *
   *
   *                            Example:
   */
  Symbol: v3_ActCode_Symbol,
  /**
   * Incident or accident is the result of a sporting accident.
   */
  SportingAccident: v3_ActCode_SportingAccident,
  /**
   * Description:Specimen has been received by the participating organization/department.
   */
  SpecimenReceived: v3_ActCode_SpecimenReceived,
  /**
   * An encounter where the patient is admitted to a health care facility for a predetermined length of time, usually less than 24 hours.
   */
  ShortStay: v3_ActCode_ShortStay,
  /**
   * Policy for handling information related to a provider of sensitive services, which will be afforded heightened confidentiality.  Description:  Policies may govern sensitivity of information related to providers who deliver sensitive healthcare services in order to protect the privacy, well-being, and safety of the provider and of patients receiving sensitive services.
   *
   *
   *                            Usage Notes: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  SensitiveServiceProviderInformationSensitivity: v3_ActCode_SensitiveServiceProviderInformationSensitivity,
  /**
   * Description:Specimen has been placed into storage at a participating location.
   */
  SpecimenInStorage: v3_ActCode_SpecimenInStorage,
  /**
   * Policy for handling sexually transmitted disease information, which will be afforded heightened confidentiality.
   *  Information handling protocols based on organizational policies related to sexually transmitted disease information that is deemed sensitive.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  SexuallyTransmittedDiseaseInformationSensitivity: v3_ActCode_SexuallyTransmittedDiseaseInformationSensitivity,
  /**
   * The act of putting something away for safe keeping. The "something" may be physical object such as a specimen, or information, such as observations regarding a specimen.
   */
  Storage: v3_ActCode_Storage,
  /**
   * Description:Specimen has been put in transit to a participating receiver.
   */
  SpecimenInTransit: v3_ActCode_SpecimenInTransit,
  /**
   * Describes the strata for which the measure is to be evaluated. There are three examples of reasons for stratification based on existing work. These include: (1) evaluate the measure based on different age groupings within the population described in the measure (e.g., evaluate the whole [age 14-25] and each sub-stratum [14-19] and [20-25]); (2) evaluate the eMeasure based on either a specific condition, a specific discharge location, or both; (3) evaluate the eMeasure based on different locations within a facility (e.g., evaluate the overall rate for all intensive care units and also some strata include additional findings [specific birth weights for neonatal intensive care units]).
   */
  Stratification: v3_ActCode_Stratification,
  /**
   * Proposed therapy may be inappropriate or ineffective because the start of administration is too late after the onset of the condition
   */
  StartTooLateAlert_2: v3_ActCode_StartTooLateAlert_2,
  /**
   * Definition: A health insurance policy that covers benefits for substance use services.
   */
  SubstanceUsePolicy: v3_ActCode_SubstanceUsePolicy,
  /**
   * Definition: Government administered and funded substance use program for beneficiaries meeting financial, substance use behavior, and health status criteria.  Beneficiaries may be required to enroll as a result of legal proceedings.  Administration, funding levels, eligibility criteria, covered benefits, provider types, and financial participation are typically set by a regulatory process.  Payer responsibilities for administering the program may be delegated to contractors.
   *
   *
   *                            Example: In the U.S., states receive funding for substance use programs from the Substance Abuse Mental Health Administration (SAMHSA).
   */
  SubstanceUseProgram: v3_ActCode_SubstanceUseProgram,
  /**
   * Definition: A government health program that provides coverage on a fee for service basis for health services to persons meeting eligibility criteria such as income, location of residence, access to other coverages, health condition, and age, the cost of which is to some extent subsidized by public funds.
   *
   *
   *                            Discussion: The structure and business processes for underwriting and administering a subsidized fee for service program is further specified by the Underwriter and Payer Role.class and Role.code.
   */
  SubsidizedFeeForServiceProgram: v3_ActCode_SubsidizedFeeForServiceProgram,
  /**
   * Definition: A government health program that provides coverage for health services to persons meeting eligibility criteria such as income, location of residence, access to other coverages, health condition, and age, the cost of which is to some extent subsidized by public funds.
   */
  SubsidizedHealthProgram: v3_ActCode_SubsidizedHealthProgram,
  /**
   * Definition: A government health program that provides coverage through managed care contracts for health services to persons meeting eligibility criteria such as income, location of residence, access to other coverages, health condition, and age, the cost of which is to some extent subsidized by public funds.
   *
   *
   *                            Discussion: The structure and business processes for underwriting and administering a subsidized managed care program is further specified by the Underwriter and Payer Role.class and Role.code.
   */
  SubsidizedManagedCareProgram: v3_ActCode_SubsidizedManagedCareProgram,
  /**
   * Description: An interaction where the exposure participants shared or co-used a common substance (e.g. drugs, needles, or common food item).
   */
  CommonSubstanceInteraction: v3_ActCode_CommonSubstanceInteraction,
  /**
   * Definition: A government health program that provides coverage for health services to persons meeting eligibility criteria for a supplemental health policy or program such as income, location of residence, access to other coverages, health condition, and age, the cost of which is to some extent subsidized by public funds.
   *
   *
   *                            Example:  Supplemental health coverage program may cover the cost of a health program or policy financial participations, such as the copays and the premiums, and may provide coverage for services in addition to those covered under the supplemented health program or policy.  In the U.S., Medicaid programs may pay the premium for a covered party who is also covered under the  Medicare program or a private health policy.
   *
   *
   *                            Discussion: The structure and business processes for underwriting and administering a subsidized supplemental retiree health program is further specified by the Underwriter and Payer Role.class and Role.code.
   */
  SubsidizedSupplementalHealthProgram: v3_ActCode_SubsidizedSupplementalHealthProgram,
  /**
   * Policy for handling information related to alcohol or drug use disorders and conditions caused by these disorders, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  SubstanceUseDisorderInformationSensitivity: v3_ActCode_SubstanceUseDisorderInformationSensitivity,
  /**
   * A diet that is not intended to be complete but is added to other diets.
   */
  NutritionalSupplement: v3_ActCode_NutritionalSupplement,
  /**
   * Description: One or more records in the query response have been suppressed due to consent or privacy restrictions.
   */
  RecordSuppressed: v3_ActCode_RecordSuppressed,
  /**
   * Provision of surgical treatment.
   */
  Surgical: v3_ActCode_Surgical,
  /**
   * Definition:
   *
   *
   *
   *
   *                               A risk or part of a risk for which there is no normal insurance market available.
   *
   *
   *
   *                               Insurance written by unauthorized insurance companies. Surplus lines insurance is insurance placed with unauthorized insurance companies through licensed surplus lines agents or brokers.
   */
  SurplusLineInsurancePolicy: v3_ActCode_SurplusLineInsurancePolicy,
  /**
   * This is not really a diet, since it contains little nutritional value, but is essentially just water.  Used before coloscopy examinations.
   */
  TeaOnly: v3_ActCode_TeaOnly,
  /**
   * A fill where the remainder of a 'complete' fill is provided after a trial fill has been provided.
   */
  TrialBalance: v3_ActCode_TrialBalance,
  /**
   * Policy for handling information not to be initially disclosed or discussed with patient except by a physician assigned to patient in this case. Information handling protocols based on organizational policies related to sensitive patient information that must be initially discussed with the patient by an attending physician before being disclosed to the patient.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   *
   *
   *                            Open Issue: This definition conflates a rule and a characteristic, and there may be a similar issue with ts sibling codes.
   */
  Taboo: v3_ActCode_Taboo,
  /**
   * A fill where the remainder of a 'complete' fill is provided after a trial fill has been provided and where the strength supplied is less than the ordered strength (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  TrialBalancePartialStrength: v3_ActCode_TrialBalancePartialStrength,
  /**
   * Description: The patient's teacher when immunized.
   */
  Teacher: v3_ActCode_Teacher,
  /**
   * A fill where a small portion is provided to allow for determination of the therapy effectiveness and patient tolerance.
   */
  TrialFill: v3_ActCode_TrialFill,
  /**
   * A fill where a small portion is provided to allow for determination of the therapy effectiveness and patient tolerance and also where the strength supplied is less than the ordered strength (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  TrialFillPartialStrength: v3_ActCode_TrialFillPartialStrength,
  /**
   * Description:Proposed therapy may be inappropriate or ineffective based on the proposed start or end time.
   */
  TimingDetectedIssue: v3_ActCode_TimingDetectedIssue,
  /**
   * A sequence of values in the "absolute" time domain.  This is the same time domain that all HL7 timestamps use.  It is time as measured by the Gregorian calendar
   */
  AbsoluteTimeSequence: v3_ActCode_AbsoluteTimeSequence,
  /**
   * A sequence of values in a "relative" time domain.  The time is measured relative to the earliest effective time in the Observation Series containing this sequence.
   */
  RelativeTimeSequence: v3_ActCode_RelativeTimeSequence,
  /**
   * Definition:The therapy is being performed at a time which diverges from the time the therapy was requested
   */
  EventTimingIncorrectAlert: v3_ActCode_EventTimingIncorrectAlert,
  /**
   * Title 38 Part 1-protected information may only be disclosed to a third party with the special written consent of the patient except where expressly authorized by 38 USC 7332. VA may disclose this information for specific purposes to: VA employees on a need to know basis - more restrictive than Privacy Act need to know; contractors who need the information in order to perform or fulfil the duties of the contract; and researchers who provide assurances that the information will not be identified in any report. This information may also be disclosed without consent where patient lacks decision-making capacity; in a medical emergency for the purpose of treating a condition which poses an immediate threat to the health of any individual and which requires immediate medical intervention; for eye, tissue, or organ donation purposes; and disclosure of HIV information for public health purposes.
   *
   *
   *                            Definition: Title 38 Part 1 - Section 1.462 Confidentiality restrictions.
   *
   *                         (a) General. The patient records to which Sections 1.460 through 1.499 of this part apply may be disclosed or used only as permitted by these regulations and may not otherwise be disclosed or used in any civil, criminal, administrative, or legislative proceedings conducted by any Federal, State, or local authority. Any disclosure made under these regulations must be limited to that information which is necessary to carry out the purpose of the disclosure. SUBCHAPTER III--PROTECTION OF PATIENT RIGHTS Sec. 7332. Confidentiality of certain medical records (a)(1) Records of the identity, diagnosis, prognosis, or treatment of any patient or subject which are maintained in connection with the performance of any program or activity (including education, training, treatment, rehabilitation, or research) relating to drug abuse, alcoholism or alcohol abuse, infection with the human immunodeficiency virus, or sickle cell anemia which is carried out by or for the Department under this title shall, except as provided in subsections (e) and (f), be confidential, and (section 5701 of this title to the contrary notwithstanding) such records may be disclosed only for the purposes and under the circumstances expressly authorized under subsection (b).
   *
   *
   *                            Usage Note: May be associated with an Act or a Role to indicate the legal provision to which the assignment of an Act.confidentialityCode or Role.confidentialityCode complies.
   */
  Title38Section7332: v3_ActCode_Title38Section7332,
  /**
   * Definition: Life insurance under which the benefit is payable only if the insured dies during a specified period. If an insured dies during that period, the beneficiary receives the death payments. If the insured survives, the policy ends and the beneficiary receives nothing.
   */
  TermLifeInsurancePolicy: v3_ActCode_TermLifeInsurancePolicy,
  /**
   * The patient is receiving a subsequent fill significantly later than would be expected based on the amount previously supplied and the therapy dosage instructions
   */
  RefillTooLateAlert: v3_ActCode_RefillTooLateAlert,
  /**
   * The patient is receiving a subsequent fill significantly earlier than would be expected based on the amount previously supplied and the therapy dosage instructions
   */
  RefillTooSoonAlert: v3_ActCode_RefillTooSoonAlert,
  /**
   * Proposed therapy may interact with an existing or recent therapeutic product
   */
  TherapeuticProductAlert: v3_ActCode_TherapeuticProductAlert,
  /**
   * Fees deducted on behalf of a payee for charges based on a per-transaction or time-period (e.g. monthly) fee.
   */
  TransactionFee: v3_ActCode_TransactionFee,
  /**
   * Can be a URL or hyperlinks that link to the transmission formats that are specified for a particular reporting program.
   */
  TransmissionFormat: v3_ActCode_TransmissionFormat,
  /**
   * Transfer of ownership for a product.
   */
  Transfer: v3_ActCode_Transfer,
  /**
   * A charge to cover the cost of travel time and/or cost in conjuction with providing a service or product. It may be charged per kilometer or per hour based on the effective agreement.
   */
  Travel: v3_ActCode_Travel,
  /**
   * Description: An interaction where the exposure participants traveled together in/on the same vehicle/trip (e.g. concurrent co-passengers).
   */
  CommonTravelInteraction: v3_ActCode_CommonTravelInteraction,
  /**
   * Communication of an agent from one living subject to another living subject through direct contact with blood or blood products where the contact with blood is part of  a therapeutic procedure.
   */
  TransfusionTransmission: v3_ActCode_TransfusionTransmission,
  /**
   * Type of security metadata about the formal declaration by an authority or neutral third party that validates the technical, security, trust, and business practice conformance of Trust Agents to facilitate security, interoperability, and trust among participants within a security domain or trust framework.
   */
  TrustAccreditation: v3_ActCode_TrustAccreditation,
  /**
   * Type of security metadata observation made about the formal declaration by an authority or neutral third party that validates the technical, security, trust, and business practice conformance of Trust Agents to facilitate security, interoperability, and trust among participants within a security domain or trust framework.
   */
  TrustAccreditationObservation: v3_ActCode_TrustAccreditationObservation,
  /**
   * Type of security metadata about privacy and security requirements with which a security domain must comply. [ISO IEC 10181-1]
   */
  TrustAgreement: v3_ActCode_TrustAgreement,
  /**
   * Type of security metadata observation made about privacy and security requirements with which a security domain must comply. [ISO IEC 10181-1]
   */
  TrustAgreementObservation: v3_ActCode_TrustAgreementObservation,
  /**
   * Type of security metadata about the digital quality or reliability of a trust assertion, activity, capability, information exchange, mechanism, process, or protocol.
   */
  TrustAssurance: v3_ActCode_TrustAssurance,
  /**
   * Type of security metadata about a set of security-relevant data issued by a security authority or trusted third party, together with security information which is used to provide the integrity and data origin authentication services for an IT resource (data, information object, service, or system capability). [Based on ISO IEC 10181-1]
   */
  TrustCertificate: v3_ActCode_TrustCertificate,
  /**
   * Type of security metadata observation made about a set of security-relevant data issued by a security authority or trusted third party, together with security information which is used to provide the integrity and data origin authentication services for an IT resource (data, information object, service, or system capability). [Based on ISO IEC 10181-1]
   *
   *
   *                            For example,
   *
   *
   *
   *                            A Certificate Policy (CP), which is a named set of rules that indicates the applicability of a certificate to a particular community and/or class of application with common security requirements. For example, a particular Certificate Policy might indicate the applicability of a type of certificate to the authentication of electronic data interchange transactions for the trading of goods within a given price range. [Trust Service Principles and Criteria for Certification Authorities Version 2.0 March 2011 Copyright 2011 by Canadian Institute of Chartered Accountants.
   *                            A Certificate Practice Statement (CSP), which is a statement of the practices which an Authority employs in issuing and managing certificates. [Trust Service Principles and Criteria for Certification Authorities Version 2.0 March 2011 Copyright 2011 by Canadian Institute of Chartered Accountants.]
   */
  TrustCertificateObservation: v3_ActCode_TrustCertificateObservation,
  /**
   * Type of security metadata about a complete set of contracts, regulations, or commitments that enable participating actors to rely on certain assertions by other actors to fulfill their information security requirements. [Kantara Initiative]
   */
  TrustFramework: v3_ActCode_TrustFramework,
  /**
   * Type of security metadata observation made about a complete set of contracts, regulations or commitments that enable participating actors to rely on certain assertions by other actors to fulfill their information security requirements. [Kantara Initiative]
   */
  TrustFrameworkObservation: v3_ActCode_TrustFrameworkObservation,
  /**
   * Type of security metadata observation made about the digital quality or reliability of a trust assertion, activity, capability, information exchange, mechanism, process, or protocol.
   */
  TrustAssuranceObservation: v3_ActCode_TrustAssuranceObservation,
  /**
   * Type of security metadata about a security architecture system component that supports enforcement of security policies.
   */
  TrustMechanism: v3_ActCode_TrustMechanism,
  /**
   * Type of security metadata observation made about a security architecture system component that supports enforcement of security policies.
   */
  TrustMechanismObservation: v3_ActCode_TrustMechanismObservation,
  /**
   * A supply action that provides sufficient material for a single dose.
   */
  UnitDose: v3_ActCode_UnitDose,
  /**
   * A supply action that provides sufficient material for a single dose via multiple products.  E.g. 2 50mg tablets for a 100mg unit dose.
   */
  UnitDoseEquivalent: v3_ActCode_UnitDoseEquivalent,
  /**
   * The filtration of a colloidal substance through a semipermeable medium that allows only the passage of small molecules.
   */
  Ultrafiltration: v3_ActCode_Ultrafiltration,
  /**
   * Definition: Life insurance under which the benefit is payable upon the insuredaTMs death or diagnosis of a terminal illness.  If an insured dies during that period, the beneficiary receives the death payments. If the insured survives, the policy ends and the beneficiary receives nothing
   */
  UniversalLifeInsurancePolicy: v3_ActCode_UniversalLifeInsurancePolicy,
  /**
   * Definition: A form of insurance protection that provides additional liability coverage after the limits of your underlying policy are reached. An umbrella liability policy also protects you (the insured) in many situations not covered by the usual liability policies.
   */
  UmbrellaLiabilityInsurancePolicy: v3_ActCode_UmbrellaLiabilityInsurancePolicy,
  /**
   * Definition: An automobile insurance policy under which the insurance company will indemnify a loss for which another motorist is liable if that motorist is unable to pay because he or she is uninsured.  Coverage under the policy applies to bodily injury damages only.  Injuries to the covered party caused by a hit-and-run driver are also covered.
   */
  UninsuredMotoristPolicy: v3_ActCode_UninsuredMotoristPolicy,
  /**
   * Maximum unit price that will be covered for the authorized product or service.
   */
  UnitPrice: v3_ActCode_UnitPrice,
  /**
   * Maximum number of items that will be covered of the product or service specified.
   */
  UnitQuantity: v3_ActCode_UnitQuantity,
  /**
   * The service provided is not related to another billed service. For example, 2 unrelated services provided on the same day to the same patient which may normally result in a refused payment for one of the items.
   */
  UnrelatedService: v3_ActCode_UnrelatedService,
  /**
   * Description:United Nations Standard Products and Services Classification, managed by Uniform Code Council (UCC): www.unspsc.org
   */
  UnitedNationsStandardProductsAndServicesClassification: v3_ActCode_UnitedNationsStandardProductsAndServicesClassification,
  /**
   * Description:Universal Product Code is one of a wide variety of bar code languages widely used in the United States and Canada for items in stores.
   */
  UniversalProductCode: v3_ActCode_UniversalProductCode,
  /**
   * Custodian security system must declassify information assigned security labels by instantiating a new version of the classified information so as to break the binding of the classifying security label when assigning a new security label that marks the information as classified at a more protected level  in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the previous assignment and binding.
   */
  UpgradeSecurityLabel: v3_ActCode_UpgradeSecurityLabel,
  /**
   * Premium paid on service fees in compensation for providing an expedited response to an urgent situation.
   */
  Urgent: v3_ActCode_Urgent,
  /**
   * Usage notes.
   */
  NoticeOfUse: v3_ActCode_NoticeOfUse,
  /**
   * Visa
   */
  Visa: v3_ActCode_Visa,
  /**
   * Indicates that the ICSR is describing a problem with the actual vaccine product such as physical defects (cloudy, particulate matter) or inability to confer immunity.
   */
  VaccineProductProblem: v3_ActCode_VaccineProductProblem,
  /**
   * Description:The specified element did not pass business-rule validation.
   */
  ValidationIssue: v3_ActCode_ValidationIssue,
  /**
   * Communication of an agent from a living subject acting as a required intermediary in the agent transmission process to a recipient living subject via direct contact.
   */
  VectorBorneTransmission: v3_ActCode_VectorBorneTransmission,
  /**
   * The provider has received a verbal permission from an authoritative source to perform the service or supply the item being invoiced.
   */
  VerbalAuthorization: v3_ActCode_VerbalAuthorization,
  /**
   * Definition: Services provided directly and through contracted and operated veteran health programs.
   */
  VeteranHealthProgram: v3_ActCode_VeteranHealthProgram,
  /**
   * Definition:Indicates that the paper version of the record has, should be or is being verified against the electronic version.
   */
  VerifyPaper: v3_ActCode_VerifyPaper,
  /**
   * Policy for handling information related to harm by violence, which is afforded heightened confidentiality. Harm by violence is perpetrated by an unrelated person.
   *
   *                         Access control concerns for information about mental or physical harm resulting from violence caused by an unrelated person may include manipulation of care givers or access to records that enable the perpetrator contact or locate the patient, but the perpetrator will likely not have established abusive psychological control over the patient.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  ViolenceInformationSensitivity: v3_ActCode_ViolenceInformationSensitivity,
  /**
   * Definition: Set of codes for a policy that provides coverage for health care expenses arising from vision services.
   *
   *                         A health insurance policy that covers benefits for vision care services, prescriptions, and products.
   */
  VisionCarePolicy: v3_ActCode_VisionCarePolicy,
  /**
   * Diet with low content of the amino-acids valin, leucin, and isoleucin, for "maple syrup disease."
   */
  LowValinLeucinIsoleucin: v3_ActCode_LowValinLeucinIsoleucin,
  /**
   * An observation that reports the volume of a sample.
   */
  ActSpecObsVolumeCode: v3_ActCode_ActSpecObsVolumeCode,
  /**
   * A patient encounter where the patient and the practitioner(s) are not in the same physical location. Examples include telephone conference, email exchange, robotic surgery, and televideo conference.
   */
  Virtual: v3_ActCode_Virtual,
  /**
   * Vision dispense invoice for up to 2 lens (left and right), frame and optional discount.  Eye exams are invoiced as a clinical service invoice.
   */
  VisionDispenseInvoice: v3_ActCode_VisionDispenseInvoice,
  /**
   * Accommodations in which there are 3 or more beds.
   */
  Ward: v3_ActCode_Ward,
  /**
   * Communication of an agent from a contaminated water source to a living subject whether the water is ingested as a food or not. The route of entry of the water may be through any bodily orifice.
   */
  WaterBorneTransmission: v3_ActCode_WaterBorneTransmission,
  /**
   * Insurance policy for injuries sustained in the work place or in the course of employment.
   */
  WorkerQuoteSCompensation: v3_ActCode_WorkerQuoteSCompensation,
  /**
   * Transaction counts and value totals for each calendar week within the date range specified.
   */
  Week: v3_ActCode_Week,
  /**
   * Description: A person enters a wellness or preventive care reminder for a given patient.
   */
  WellnessReminderListEntry: v3_ActCode_WellnessReminderListEntry,
  /**
   * Description: A person reviews a list of wellness or preventive care reminders for a given patient.
   */
  WellnessReminderListReview: v3_ActCode_WellnessReminderListReview,
  WGHT: v3_ActCode_WGHT,
  /**
   * Description: Work Injury related additional Information Attachment
   */
  WorkInjuryReportAttachment: v3_ActCode_WorkInjuryReportAttachment,
  /**
   * Description: Exposure interaction occurred in a work setting, i.e. participants are co-workers.
   */
  WorkInteraction: v3_ActCode_WorkInteraction,
  /**
   * Incident or accident is the result of a work place accident
   */
  WorkplaceAccident: v3_ActCode_WorkplaceAccident,
  /**
   * Definition: Government mandated program providing coverage, disability income, and vocational rehabilitation for injuries sustained in the work place or in the course of employment.  Employers may either self-fund the program, purchase commercial coverage, or pay a premium to a government entity that administers the program.  Employees may be required to pay premiums toward the cost of coverage as well.
   */
  WorkersCompensationProgram: v3_ActCode_WorkersCompensationProgram,
  /**
   * Used by one system to inform another that the container is no longer available within the scope of the system (e.g., tube broken or discarded).
   */
  ContainerUnavailable: v3_ActCode_ContainerUnavailable,
  /**
   * Description: Digital X-Ray Attachment
   */
  XRay: v3_ActCode_XRay,
  /**
   * Transaction counts and value totals for each calendar year within the date range specified.
   */
  Year: v3_ActCode_Year,
};
/**
 *  ActConsentDirective codes are used to specify the type of Consent Directive to which a Consent Directive Act conforms.
 */
export const V3ActConsentDirective = {
  /**
   * Specifies the type of agreement between one or more grantor and grantee in which rights and obligations related to one or more shared items of interest are allocated.
   *
   *
   *                            Usage Note: Such agreements may be considered "consent directives" or "contracts" depending on the context, and are considered closely related or synonymous from a legal perspective.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare Privacy Consent Directive permitting or restricting in whole or part the collection, access, use, and disclosure of health information, and any associated handling caveats.
   *                            Healthcare Medical Consent Directive to receive medical procedures after being informed of risks and benefits, thereby reducing the grantee's liability.
   *                            Research Informed Consent for participation in clinical trials and disclosure of health information after being informed of risks and benefits, thereby reducing the grantee's liability.
   *                            Substitute decision maker delegation in which the grantee assumes responsibility to act on behalf of the grantor.
   *                            Contracts in which the agreement requires assent/dissent by the grantor of terms offered by a grantee, a consumer opts out of an "award" system for use of a retailer's marketing or credit card vendor's point collection cards in exchange for allowing purchase tracking and profiling.
   *                            A mobile device or App privacy policy and terms of service to which a user must agree in whole or in part in order to utilize the service.
   *                            Agreements between a client and an authorization server or between an authorization server and a resource operator and/or resource owner permitting or restricting e.g., collection, access, use, and disclosure of information, and any associated handling caveats.
   */
  ActConsentDirective: v3_ActCode_ActConsentDirective,
  /**
   * This general consent directive specifically limits disclosure of health information for purpose of emergency treatment. Additional parameters may further limit the disclosure to specific users, roles, duration, types of information, and impose uses obligations.
   *
   *
   *                            Definition: Opt-in to disclosure of health information for emergency only consent directive.
   */
  EmergencyOnly: v3_ActCode_EmergencyOnly,
  /**
   * A grantor's terms of agreement to which a grantee may assent or dissent, and which may include an opportunity for a grantee to request restrictions or extensions.
   *
   *
   *                            Comment: A grantor typically is able to stipulate preferred terms of agreement when the grantor has control over the topic of the agreement, which a grantee must accept in full or may be offered an opportunity to extend or restrict certain terms.
   *
   *
   *                            Usage Note: If the grantor's term of agreement must be accepted in full, then this is considered "basic consent".  If a grantee is offered an opportunity to extend or restrict certain terms, then the agreement is considered "granular consent".
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare: A PHR account holder [grantor] may require any PHR user [grantee]  to accept the terms of agreement in full, or may permit a PHR user to extend or restrict terms selected by the account holder or requested by the PHR user.
   *                            Non-healthcare: The owner of a resource server [grantor] may require any authorization server [grantee] to meet authorization requirements stipulated in the grantor's terms of agreement.
   */
  GrantorChoice: v3_ActCode_GrantorChoice,
  /**
   * A grantor's presumed assent to the grantee's terms of agreement is based on the grantor's behavior, which may result from not expressly assenting to the consent directive offered, or from having no right to assent or dissent offered by the grantee.
   *
   *
   *                            Comment: Implied or "implicit" consent occurs when the behavior of the grantor is understood by a reasonable person to signal agreement to the grantee's terms.
   *
   *
   *                            Usage Note: Implied consent with no opportunity to assent or dissent to certain terms is considered "basic consent".
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare: A patient schedules an appointment with a provider, and either does not take the opportunity to expressly assent or dissent to the provider's consent directive, does not have an opportunity to do so, as in the case where emergency care is required, or simply behaves as though the patient [grantor] agrees to the rights granted to the provider [grantee] in an implicit consent directive.
   *                            An injured and unconscious patient is deemed to have assented to emergency treatment by those permitted to do so under jurisdictional laws, e.g., Good Samaritan laws.
   *                            Non-healthcare: Upon receiving a driver's license, the driver is deemed to have assented without explicitly consenting to undergoing field sobriety tests.
   *                            A corporation that does business in a foreign nation is deemed to have deemed to have assented without explicitly consenting to abide by that nation's laws.
   */
  ImpliedConsent: v3_ActCode_ImpliedConsent,
  /**
   * A grantor's presumed assent to the grantee's terms of agreement, which is based on the grantor's behavior, and includes a right to dissent to certain terms.
   *
   *
   *                            Comment: A grantor assenting to the grantee's terms of agreement may or may not exercise a right to dissent to grantor selected terms or to grantee's selected terms to which a grantor may dissent.
   *
   *
   *                            Usage Note: Implied or "implicit" consent with an "opportunity to dissent" occurs when the grantor's behavior is understood by a reasonable person to signal assent to the grantee's terms of agreement whether the grantor requests or the grantee approves further restrictions, is considered "granular consent".
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare Examples: A healthcare provider deems a patient's assent to disclosure of health information to family members and friends, but offers an opportunity or permits the patient to dissent to such disclosures.
   *                            A health information exchanges deems a patient to have assented to disclosure of health information for treatment purposes, but offers the patient an opportunity to dissents to disclosure to particular provider organizations.
   *                            Non-healthcare Examples: A bank deems a banking customer's assent to specified collection, access, use, or disclosure of financial information as a requirement of holding a bank account, but provides the user an opportunity to limit third-party collection, access, use or disclosure of that information for marketing purposes.
   */
  ImpliedConsentWithOpportunityToDissent: v3_ActCode_ImpliedConsentWithOpportunityToDissent,
  /**
   * No notification or opportunity is provided for a grantor to assent or dissent to a grantee's terms of agreement.
   *
   *
   *                            Comment: A "No Consent" policy scheme provides no opportunity for accommodation of an individual's preferences, and may not comply with Fair Information Practice Principles [FIPP] by enabling the data subject to object, access collected information, correct errors, or have accounting of disclosures.
   *
   *
   *                            Usage Note: The grantee's terms of agreement, may be available to the grantor by reviewing the grantee's privacy policies, but there is no notice by which a grantor is apprised of the policy directly or able to acknowledge.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare: Without notification or an opportunity to assent or dissent, a patient's health information is automatically included in and available (often according to certain rules) through a health information exchange.  Note that this differs from implied consent, where the patient is assumed to have consented.
   *                            Without notification or an opportunity to assent or dissent, a patient's health information is collected, accessed, used, or disclosed for research, public health, security, fraud prevention, court order, or law enforcement.
   *                            Non-healthcare: Without notification or an opportunity to assent or dissent, a consumer's healthcare or non-healthcare internet searches are aggregated for secondary uses such as behavioral tracking and profiling.
   *                            Without notification or an opportunity to assent or dissent, a consumer's location and activities in a shopping mall are tracked by RFID tags on purchased items.
   */
  NoConsent: v3_ActCode_NoConsent,
  /**
   * Acknowledgement of custodian notice of privacy practices.
   *
   *
   *                            Usage Notes: This type of consent directive acknowledges a custodian's notice of privacy practices including its permitted collection, access, use and disclosure of health information to users and for purposes of use specified.
   */
  NoticeOfPrivacyPractices: v3_ActCode_NoticeOfPrivacyPractices,
  /**
   * A grantor's assent to the terms of an agreement offered by a grantee without an opportunity for to dissent to any terms.
   *
   *
   *                            Comment: Acceptance of a grantee's terms pertaining, for example, to permissible activities, purposes of use, handling caveats, expiry date, and revocation policies.
   *
   *
   *                            Usage Note: Opt-in with no opportunity for a grantor to restrict certain permissions sought by the grantee is considered "basic consent".
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare: A patient [grantor] signs a provider's [grantee's] consent directive form, which lists permissible collection, access, use, or disclosure activities, purposes of use, handling caveats, and revocation policies.
   *                            Non-healthcare: An employee [grantor] signs an employer's [grantee's] non-disclosure and non-compete agreement.
   */
  OptIn: v3_ActCode_OptIn,
  /**
   * A grantor's assent to the grantee's terms of an agreement with an opportunity for to dissent to certain grantor or grantee selected terms.
   *
   *
   *                            Comment: A grantor dissenting to the grantee's terms of agreement may or may not exercise a right to assent to grantor's pre-approved restrictions or to grantee's selected terms to which a grantor may dissent.
   *
   *
   *                            Usage Note: Opt-in with restrictions is considered "granular consent" because the grantor has an opportunity to narrow the permissions sought by the grantee.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare:  A patient assent to grantee's consent directive terms for collection, access, use, or disclosure of health information, and dissents to disclosure to certain recipients as allowed by the provider's pre-approved restriction list.
   *                            Non-Healthcare: A cell phone user assents to the cell phone's privacy practices and terms of use, but dissents from location tracking by turning off the cell phone's tracking capability.
   */
  OptInWithRestrictions: v3_ActCode_OptInWithRestrictions,
  /**
   * A grantor's dissent to the terms of agreement offered by a grantee without an opportunity for to assent to any terms.
   *
   *
   *                            Comment: Rejection of a grantee's terms of agreement pertaining, for example, to permissible activities, purposes of use, handling caveats, expiry date, and revocation policies.
   *
   *
   *                            Usage Note: Opt-out with no opportunity for a grantor to permit certain permissions sought by the grantee is considered "basic consent".
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare: A patient [grantor] declines to sign a provider's [grantee's] consent directive form, which lists permissible collection, access, use, or disclosure activities, purposes of use, handling caveats, revocation policies, and consequences of not assenting.
   *                            Non-healthcare: An employee [grantor] refuses to sign an employer's [grantee's] agreement not to join unions or participate in a strike where state law protects employee's collective bargaining rights.
   *                            A citizen [grantor] refuses to enroll in mandatory government [grantee] health insurance based on religious beliefs, which is an exemption.
   */
  OpOut: v3_ActCode_OpOut,
  /**
   * A grantor's dissent to the grantee's terms of agreement except for certain grantor or grantee selected terms.
   *
   *
   *                            Comment: A rejection of a grantee's terms of agreement while assenting to certain permissions sought by the grantee or requesting approval of additional grantor terms.
   *
   *
   *                            Usage Note: Opt-out with exceptions is considered a "granular consent" because the grantor has an opportunity to accept certain permissions sought by the grantee or request additional grantor terms, while rejecting other grantee terms.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Healthcare: A patient [grantor] dissents to a health information exchange consent directive with the exception of disclosure based on a limited "time to live" shared secret [e.g., a token or password], which the patient can give to a provider when seeking care.
   *                            Non-healthcare: A social media user [grantor] dissents from public access to their account, but assents to access to a circle of friends.
   */
  OptOutWithExceptions: v3_ActCode_OptOutWithExceptions,
};
/**
 *   Definition:
 * The type of consent directive, e.g., to consent or dissent to collect, access, or use in specific ways within an EHRS or for health information exchange; or to disclose  health information  for purposes such as research.
 */
export const V3ActConsentType = {
  /**
   * Definition: The type of consent directive, e.g., to consent or dissent to collect, access, or use in specific ways within an EHRS or for health information exchange; or to disclose  health information  for purposes such as research.
   */
  ActConsentType: v3_ActCode_ActConsentType,
  /**
   * Definition: Consent to have healthcare information collected in an electronic health record.  This entails that the information may be used in analysis, modified, updated.
   */
  InformationCollection: v3_ActCode_InformationCollection,
  /**
   * Definition: Consent to have collected healthcare information disclosed.
   */
  InformationDisclosure: v3_ActCode_InformationDisclosure,
  /**
   * Definition: Consent to access healthcare information.
   */
  InformationAccess: v3_ActCode_InformationAccess,
  /**
   * Definition: Consent to access or "read" only, which entails that the information is not to be copied, screen printed, saved, emailed, stored, re-disclosed or altered in any way.  This level ensures that data which is masked or to which access is restricted will not be.
   *
   *
   *                            Example: Opened and then emailed or screen printed for use outside of the consent directive purpose.
   */
  AccessOnly: v3_ActCode_AccessOnly,
  /**
   * Definition: Consent to access and save only, which entails that access to the saved copy will remain locked.
   */
  AccessAndSaveOnly: v3_ActCode_AccessAndSaveOnly,
  /**
   * Definition: Information re-disclosed without the patient's consent.
   */
  InformationRedisclosure: v3_ActCode_InformationRedisclosure,
  /**
   * Definition: Consent to have healthcare information in an electronic health record accessed for research purposes.
   */
  ResearchInformationAccess: v3_ActCode_ResearchInformationAccess,
  /**
   * Definition: Consent to have de-identified healthcare information in an electronic health record that is accessed for research purposes, but without consent to re-identify the information under any circumstance.
   */
  DeIdentifiedInformationAccess: v3_ActCode_DeIdentifiedInformationAccess,
  /**
   * Definition: Consent to have de-identified healthcare information in an electronic health record that is accessed for research purposes re-identified under specific circumstances outlined in the consent.
   *
   *
   *                            Example:: Where there is a need to inform the subject of potential health issues.
   */
  ReIdentifiableInformationAccess: v3_ActCode_ReIdentifiableInformationAccess,
};
/**
 *   Definition:
 * Set of codes indicating the type of insurance policy or program that pays for the cost of benefits provided to covered parties.
 */
export const V3ActCoverageTypeCode = {
  /**
   * Definition: Set of codes indicating the type of insurance policy or program that pays for the cost of benefits provided to covered parties.
   */
  ActCoverageTypeCode: v3_ActCode_ActCoverageTypeCode,
  /**
   * Definition: Set of codes indicating the type of health insurance policy that covers health services provided to covered parties.  A health insurance policy is a written contract for insurance between the insurance company and the policyholder, and contains pertinent facts about the policy owner (the policy holder), the health insurance coverage, the insured subscribers and dependents, and the insurer.  Health insurance is typically administered in accordance with a plan, which specifies (1) the type of health services and health conditions that will be covered under what circumstances (e.g., exclusion of a pre-existing condition, service must be deemed medically necessary; service must not be experimental; service must provided in accordance with a protocol; drug must be on a formulary; service must be prior authorized; or be a referral from a primary care provider); (2) the type and affiliation of providers (e.g., only allopathic physicians, only in network, only providers employed by an HMO); (3) financial participations required of covered parties (e.g., co-pays, coinsurance, deductibles, out-of-pocket); and (4) the manner in which services will be paid (e.g., under indemnity or fee-for-service health plans, the covered party typically pays out-of-pocket and then file a claim for reimbursement, while health plans that have contractual relationships with providers, i.e., network providers, typically do not allow the providers to bill the covered party for the cost of the service until after filing a claim with the payer and receiving reimbursement).
   */
  ActHealthInsuranceTypeCode: v3_ActCode_ActHealthInsuranceTypeCode,
  /**
   * Set of codes indicating the type of insurance policy or other source of funds to cover healthcare costs.
   */
  ActInsurancePolicyCode: v3_ActCode_ActInsurancePolicyCode,
  /**
   * Definition: Set of codes indicating the type of insurance policy.  Insurance, in law and economics, is a form of risk management primarily used to hedge against the risk of potential financial loss. Insurance is defined as the equitable transfer of the risk of a potential loss, from one entity to another, in exchange for a premium and duty of care. A policy holder is an individual or an organization enters into a contract with an underwriter which stipulates that, in exchange for payment of a sum of money (a premium), one or more covered parties (insureds) is guaranteed compensation for losses resulting from certain perils under specified conditions.  The underwriter analyzes the risk of loss, makes a decision as to whether the risk is insurable, and prices the premium accordingly.  A policy provides benefits that indemnify or cover the cost of a loss incurred by a covered party, and may include coverage for services required to remediate a loss.  An insurance policy contains pertinent facts about the policy holder, the insurance coverage, the covered parties, and the insurer.  A policy may include exemptions and provisions specifying the extent to which the indemnification clause cannot be enforced for intentional tortious conduct of a covered party, e.g., whether the covered parties are jointly or severably insured.
   *
   *
   *                            Discussion: In contrast to programs, an insurance policy has one or more policy holders, who own the policy.  The policy holder may be the covered party, a relative of the covered party, a partnership, or a corporation, e.g., an employer.  A subscriber of a self-insured health insurance policy is a policy holder.  A subscriber of an employer sponsored health insurance policy is holds a certificate of coverage, but is not a policy holder; the policy holder is the employer.  See CoveredRoleType.
   */
  ActInsuranceTypeCode: v3_ActCode_ActInsuranceTypeCode,
  /**
   * Definition: A set of codes used to indicate coverage under a program.  A program is an organized structure for administering and funding coverage of a benefit package for covered parties meeting eligibility criteria, typically related to employment, health, financial, and demographic status. Programs are typically established or permitted by legislation with provisions for ongoing government oversight.  Regulations may mandate the structure of the program, the manner in which it is funded and administered, covered benefits, provider types, eligibility criteria and financial participation. A government agency may be charged with implementing the program in accordance to the regulation.  Risk of loss under a program in most cases would not meet what an underwriter would consider an insurable risk, i.e., the risk is not random in nature, not financially measurable, and likely requires subsidization with government funds.
   *
   *
   *                            Discussion: Programs do not have policy holders or subscribers.  Program eligibles are enrolled based on health status, statutory eligibility, financial status, or age.  Program eligibles who are covered parties under the program may be referred to as members, beneficiaries, eligibles, or recipients.  Programs risk are underwritten by not for profit organizations such as governmental entities, and the beneficiaries typically do not pay for any or some portion of the cost of coverage.  See CoveredPartyRoleType.
   */
  ActProgramTypeCode: v3_ActCode_ActProgramTypeCode,
  /**
   * Definition: A policy that, after an initial premium or premiums, pays out a sum at pre-determined intervals.
   *
   *                         For example, a policy holder may pay $10,000, and in return receive $150 each month until he dies; or $1,000 for each of 14 years or death benefits if he dies before the full term of the annuity has elapsed.
   */
  AnnuityPolicy: v3_ActCode_AnnuityPolicy,
  /**
   * Insurance policy for injuries sustained in an automobile accident.  Will also typically covered non-named parties to the policy, such as pedestrians 	and passengers.
   */
  Automobile: v3_ActCode_Automobile,
  /**
   * Definition: A program that provides low-income, uninsured, and underserved women access to timely, high-quality screening and diagnostic services, to detect breast and cervical cancer at the earliest stages.
   *
   *
   *                            Example: To improve women's access to screening for breast and cervical cancers, Congress passed the Breast and Cervical Cancer Mortality Prevention Act of 1990, which guided CDC in creating the National Breast and Cervical Cancer Early Detection Program (NBCCEDP), which  provides access to critical breast and cervical cancer screening services for underserved women in the United States.  An estimated 7 to 10% of U.S. women of screening age are eligible to receive NBCCEDP services. Federal guidelines establish an eligibility baseline to direct services to uninsured and underinsured women at or below 250% of federal poverty level; ages 18 to 64 for cervical screening; ages 40 to 64 for breast screening.
   */
  WomenQuoteSCancerDetectionProgram: v3_ActCode_WomenQuoteSCancerDetectionProgram,
  /**
   * Definition: A program that covers the cost of services provided directly to a beneficiary who typically has no other source of coverage without charge.
   */
  CharityProgram: v3_ActCode_CharityProgram,
  /**
   * Definition: An automobile insurance policy under which the insurance company will cover the cost of damages to an automobile owned by the named insured that are caused by accident or intentionally by another party.
   */
  CollisionCoveragePolicy: v3_ActCode_CollisionCoveragePolicy,
  /**
   * Definition: A program that covers the cost of services provided to crime victims for injuries or losses related to the occurrence of a crime.
   */
  CrimeVictimProgram: v3_ActCode_CrimeVictimProgram,
  /**
   * Definition: A health insurance policy that that covers benefits for dental services.
   */
  DentalCarePolicy: v3_ActCode_DentalCarePolicy,
  /**
   * Definition: A public or government health program that administers and funds coverage for dental care to assist program eligible who meet financial and health status criteria.
   */
  DentalProgram: v3_ActCode_DentalProgram,
  /**
   * Definition: An insurance policy that provides a regular payment to compensate for income lost due to the covered party's inability to work because of illness or injury.
   */
  DisabilityInsurancePolicy: v3_ActCode_DisabilityInsurancePolicy,
  /**
   * Definition: A health insurance policy that covers benefits for healthcare services provided for named conditions under the policy, e.g., cancer, diabetes, or HIV-AIDS.
   */
  DiseaseSpecificPolicy: v3_ActCode_DiseaseSpecificPolicy,
  /**
   * Definition: A public or government health program that administers and funds coverage for health and social services to assist program eligible who meet financial and health status criteria related to a particular disease.
   *
   *
   *                            Example: Reproductive health, sexually transmitted disease, and end renal disease programs.
   */
  PublicHealthProgram: v3_ActCode_PublicHealthProgram,
  /**
   * Definition: A health insurance policy that covers benefits for prescription drugs, pharmaceuticals, and supplies.
   */
  DrugPolicy: v3_ActCode_DrugPolicy,
  /**
   * Definition: An employee assistance program is run by an employer or employee organization for the purpose of providing benefits and covering all or part of the cost for employees to receive counseling, referrals, and advice in dealing with stressful issues in their lives. These may include substance abuse, bereavement, marital problems, weight issues, or general wellness issues.  The services are usually provided by a third-party, rather than the company itself, and the company receives only summary statistical data from the service provider. Employee's names and services received are kept confidential.
   */
  EmployeeAssistanceProgram: v3_ActCode_EmployeeAssistanceProgram,
  /**
   * Private insurance policy that provides coverage in addition to other policies (e.g. in addition to a Public Healthcare insurance policy).
   */
  ExtendedHealthcare: v3_ActCode_ExtendedHealthcare,
  /**
   * Definition: A public or government program that administers publicly funded coverage of kidney dialysis and kidney transplant services.
   *
   *                         Example: In the U.S., the Medicare End-stage Renal Disease program (ESRD), the National Kidney Foundation (NKF) American Kidney Fund (AKF) The Organ Transplant Fund.
   */
  EndRenalProgram: v3_ActCode_EndRenalProgram,
  /**
   * Definition: An insurance policy under a benefit plan run by an employer or employee organization for the purpose of providing benefits other than pension-related to employees and their families. Typically provides health-related benefits, benefits for disability, disease or unemployment, or day care and scholarship benefits, among others.  An employer sponsored health policy includes coverage of health care expenses arising from sickness or accidental injury, coverage for on-site medical clinics or for dental or vision benefits, which are typically provided under a separate policy.  Coverage excludes health care expenses covered by accident or disability, workers' compensation, liability or automobile insurance.
   */
  EmployeeWelfareBenefitPlanPolicy: v3_ActCode_EmployeeWelfareBenefitPlanPolicy,
  /**
   * Definition:  An insurance policy that covers qualified benefits under a Flexible Benefit plan such as group medical insurance, long and short term disability income insurance, group term life insurance for employees only up to $50,000 face amount, specified disease coverage such as a cancer policy, dental and/or vision insurance, hospital indemnity insurance, accidental death and dismemberment insurance, a medical expense reimbursement plan and a dependent care reimbursement plan.
   *
   *
   *                             Discussion: See UnderwriterRoleTypeCode flexible benefit plan which is defined as a benefit plan that allows employees to choose from several life, health, disability, dental, and other insurance plans according to their individual needs. Also known as cafeteria plans.  Authorized under Section 125 of the Revenue Act of 1978.
   */
  FlexibleBenefitPlanPolicy: v3_ActCode_FlexibleBenefitPlanPolicy,
  /**
   * Definition: A set of codes used to indicate a government program that is an organized structure for administering and funding coverage of a benefit package for covered parties meeting eligibility criteria, typically related to employment, health and financial status. Government programs are established or permitted by legislation with provisions for ongoing government oversight.  Regulation mandates the structure of the program, the manner in which it is funded and administered, covered benefits, provider types, eligibility criteria and financial participation. A government agency is charged with implementing the program in accordance to the regulation
   *
   *
   *                            Example: Federal employee health benefit program in the U.S.
   */
  GovernmentEmployeeHealthProgram: v3_ActCode_GovernmentEmployeeHealthProgram,
  /**
   * Definition: A health insurance policy that covers healthcare benefits by protecting covered parties from medical expenses arising from health conditions, sickness, or accidental injury as well as preventive care. Health insurance policies explicitly exclude coverage for losses insured under a disability policy, workers' compensation program, liability insurance (including automobile insurance); or for medical expenses, coverage for on-site medical clinics or for limited dental or vision benefits when these are provided under a separate policy.
   *
   *
   *                            Discussion: Health insurance policies are offered by health insurance plans that typically reimburse providers for covered services on a fee-for-service basis, that is, a fee that is the allowable amount that a provider may charge.  This is in contrast to managed care plans, which typically prepay providers a per-member/per-month amount or capitation as reimbursement for all covered services rendered.  Health insurance plans include indemnity and healthcare services plans.
   */
  HealthInsurancePlanPolicy: v3_ActCode_HealthInsurancePlanPolicy,
  /**
   * Definition: A government program that provides health coverage to individuals who are considered medically uninsurable or high risk, and who have been denied health insurance due to a serious health condition. In certain cases, it also applies to those who have been quoted very high premiums a" again, due to a serious health condition.  The pool charges premiums for coverage.  Because the pool covers high-risk people, it incurs a higher level of claims than premiums can cover. The insurance industry pays into the pool to make up the difference and help it remain viable.
   */
  HighRiskPoolProgram: v3_ActCode_HighRiskPoolProgram,
  /**
   * Definition: Government administered and funded HIV-AIDS program for beneficiaries meeting financial and health status criteria.  Administration, funding levels, eligibility criteria, covered benefits, provider types, and financial participation are typically set by a regulatory process.  Payer responsibilities for administering the program may be delegated to contractors.
   *
   *
   *                            Example: In the U.S., the Ryan White program, which is administered by the Health Resources and Services Administration.
   */
  HIVAIDSProgram: v3_ActCode_HIVAIDSProgram,
  /**
   * Definition: A policy for a health plan that provides coverage for health care only through contracted or employed physicians and hospitals located in particular geographic or service areas.  HMOs emphasize prevention and early detection of illness. Eligibility to enroll in an HMO is determined by where a covered party lives or works.
   */
  HealthMaintenanceOrganizationPolicy: v3_ActCode_HealthMaintenanceOrganizationPolicy,
  /**
   * Insurance policy that provides for an allotment of funds replenished on a periodic (e.g. annual) basis. The use of the funds under this policy is at the 	discretion of the covered party.
   */
  HealthSpendingAccount: v3_ActCode_HealthSpendingAccount,
  /**
   * Definition: Services provided directly and through contracted and operated indigenous peoples health programs.
   *
   *
   *                            Example: Indian Health Service in the U.S.
   */
  IndigenousPeoplesHealthProgram: v3_ActCode_IndigenousPeoplesHealthProgram,
  /**
   * Definition: A policy under which the insurer agrees to pay a sum of money upon the occurrence of the covered partys death. In return, the policyholder agrees to pay a stipulated amount called a premium at regular intervals.  Life insurance indemnifies the beneficiary for the loss of the insurable interest that a beneficiary has in the life of a covered party.  For persons related by blood, a substantial interest established through love and affection, and for all other persons, a lawful and substantial economic interest in having the life of the insured continue. An insurable interest is required when purchasing life insurance on another person. Specific exclusions are often written into the contract to limit the liability of the insurer; for example claims resulting from suicide or relating to war, riot and civil commotion.
   *
   *
   *                            Discussion:A life insurance policy may be used by the covered party as a source of health care coverage in the case of  a viatical settlement, which is the sale of a life insurance policy by the policy owner, before the policy matures. Such a sale, at a price discounted from the face amount of the policy but usually in excess of the premiums paid or current cash surrender value, provides the seller an immediate cash settlement. Generally, viatical settlements involve insured individuals with a life expectancy of less than two years. In countries without state-subsidized healthcare and high healthcare costs (e.g. United States), this is a practical way to pay extremely high health insurance premiums that severely ill people face. Some people are also familiar with life settlements, which are similar transactions but involve insureds with longer life expectancies (two to fifteen years).
   */
  LifeInsurancePolicy: v3_ActCode_LifeInsurancePolicy,
  /**
   * Definition: An insurance policy that covers benefits for long-term care services people need when they no longer can care for themselves. This may be due to an accident, disability, prolonged illness or the simple process of aging. Long-term care services assist with activities of daily living including:
   *
   *
   *
   *                               Help at home with day-to-day activities, such as cooking, cleaning, bathing and dressing
   *
   *
   *
   *                               Care in the community, such as in an adult day care facility
   *
   *
   *
   *                               Supervised care provided in an assisted living facility
   *
   *
   *
   *                               Skilled care provided in a nursing home
   */
  LongTermCarePolicy: v3_ActCode_LongTermCarePolicy,
  /**
   * mandatory health program
   */
  MandatoryHealthProgram: v3_ActCode_MandatoryHealthProgram,
  /**
   * Definition: Government mandated program providing coverage, disability income, and vocational rehabilitation for injuries sustained in the work place or in the course of employment.  Employers may either self-fund the program, purchase commercial coverage, or pay a premium to a government entity that administers the program.  Employees may be required to pay premiums toward the cost of coverage as well.
   *
   *                         Managed care policies specifically exclude coverage for losses insured under a disability policy, workers' compensation program, liability insurance (including automobile insurance); or for medical expenses, coverage for on-site medical clinics or for limited dental or vision benefits when these are provided under a separate policy.
   *
   *
   *                            Discussion: Managed care policies are offered by managed care plans that contract with selected providers or health care organizations to provide comprehensive health care at a discount to covered parties and coordinate the financing and delivery of health care. Managed care uses medical protocols and procedures agreed on by the medical profession to be cost effective, also known as medical practice guidelines. Providers are typically reimbursed for covered services by a capitated amount on a per member per month basis that may reflect difference in the health status and level of services anticipated to be needed by the member.
   */
  ManagedCarePolicy: v3_ActCode_ManagedCarePolicy,
  /**
   * Definition: A health insurance policy that covers benefits for mental health services and prescriptions.
   */
  MentalHealthPolicy: v3_ActCode_MentalHealthPolicy,
  /**
   * Definition: Government administered and funded mental health program for beneficiaries meeting financial and mental health status criteria.  Administration, funding levels, eligibility criteria, covered benefits, provider types, and financial participation are typically set by a regulatory process.  Payer responsibilities for administering the program may be delegated to contractors.
   *
   *
   *                            Example: In the U.S., states receive funding for substance use programs from the Substance Abuse Mental Health Administration (SAMHSA).
   */
  MentalHealthProgram: v3_ActCode_MentalHealthProgram,
  /**
   * Definition: A government program that provides coverage for health services to military personnel, retirees, and dependents.  A covered party who is a subscriber can choose from among Fee-for-Service (FFS) plans, and their Preferred Provider Organizations (PPO), or Plans offering a Point of Service (POS) Product, or Health Maintenance Organizations.
   *
   *
   *                            Example: In the U.S., TRICARE, CHAMPUS.
   */
  MilitaryHealthProgram: v3_ActCode_MilitaryHealthProgram,
  /**
   * Definition: A type of insurance that covers damage to or loss of the policyholderaTMs property by providing payments for damages to property damage or the injury or death of living subjects.  The terms "casualty" and "liability" insurance are often used interchangeably. Both cover the policyholder's legal liability for damages caused to other persons and/or their property.
   */
  PropertyAndCasualtyInsurancePolicy: v3_ActCode_PropertyAndCasualtyInsurancePolicy,
  /**
   * Definition: A policy for a health plan that has features of both an HMO and a FFS plan.  Like an HMO, a POS plan encourages the use its HMO network to maintain discounted fees with participating providers, but recognizes that sometimes covered parties want to choose their own provider.  The POS plan allows a covered party to use providers who are not part of the HMO network (non-participating providers).  However, there is a greater cost associated with choosing these non-network providers. A covered party will usually pay deductibles and coinsurances that are substantially higher than the payments when he or she uses a plan provider. Use of non-participating providers often requires the covered party to pay the provider directly and then to file a claim for reimbursement, like in an FFS plan.
   */
  PointOfServicePolicy: v3_ActCode_PointOfServicePolicy,
  /**
   * Definition: A network-based, managed care plan that allows a covered party to choose any health care provider. However, if care is received from a "preferred" (participating in-network) provider, there are generally higher benefit coverage and lower deductibles.
   */
  PreferredProviderOrganizationPolicy: v3_ActCode_PreferredProviderOrganizationPolicy,
  /**
   * Insurance policy funded by a public health system such as a provincial or national health plan.  Examples include BC MSP (British Columbia 	Medical Services Plan) OHIP (Ontario Health Insurance Plan), NHS (National Health Service).
   */
  PublicHealthcare: v3_ActCode_PublicHealthcare,
  /**
   * Definition: An agreement between two or more insurance companies by which the risk of loss is proportioned. Thus the risk of loss is spread and a disproportionately large loss under a single policy does not fall on one insurance company. Acceptance by an insurer, called a reinsurer, of all or part of the risk of loss of another insurance company.
   *
   *
   *                            Discussion: Reinsurance is a means by which an insurance company can protect itself against the risk of losses with other insurance companies. Individuals and corporations obtain insurance policies to provide protection for various risks (hurricanes, earthquakes, lawsuits, collisions, sickness and death, etc.). Reinsurers, in turn, provide insurance to insurance companies.
   *
   *                         For example, an HMO may purchase a reinsurance policy to protect itself from losing too much money from one insured's particularly expensive health care costs. An insurance company issuing an automobile liability policy, with a limit of $100,000 per accident may reinsure its liability in excess of $10,000. A fire insurance company which issues a large policy generally reinsures a portion of the risk with one or several other companies. Also called risk control insurance or stop-loss insurance.
   */
  ReinsurancePolicy: v3_ActCode_ReinsurancePolicy,
  /**
   * Definition: A government mandated program with specific eligibility requirements based on premium contributions made during employment, length of employment, age, and employment status, e.g., being retired, disabled, or a dependent of a covered party under this program.   Benefits typically include ambulatory, inpatient, and long-term care, such as hospice care, home health care and respite care.
   */
  RetireeHealthProgram: v3_ActCode_RetireeHealthProgram,
  /**
   * Definition: Government administered and funded program to support provision of care to underserved populations through safety net clinics.
   *
   *
   *                            Example: In the U.S., safety net providers such as federally qualified health centers (FQHC) receive funding under PHSA Section 330 grants administered by the Health Resources and Services Administration.
   */
  SafetyNetClinicProgram: v3_ActCode_SafetyNetClinicProgram,
  /**
   * Definition: A social service program funded by a public or governmental entity.
   *
   *
   *                            Example: Programs providing habilitation, food, lodging, medicine, transportation, equipment, devices, products, education, training, counseling, alteration of living or work space, and other resources to persons meeting eligibility criteria.
   */
  SocialServiceProgram: v3_ActCode_SocialServiceProgram,
  /**
   * Definition: A health insurance policy that covers benefits for substance use services.
   */
  SubstanceUsePolicy: v3_ActCode_SubstanceUsePolicy,
  /**
   * Definition: Government administered and funded substance use program for beneficiaries meeting financial, substance use behavior, and health status criteria.  Beneficiaries may be required to enroll as a result of legal proceedings.  Administration, funding levels, eligibility criteria, covered benefits, provider types, and financial participation are typically set by a regulatory process.  Payer responsibilities for administering the program may be delegated to contractors.
   *
   *
   *                            Example: In the U.S., states receive funding for substance use programs from the Substance Abuse Mental Health Administration (SAMHSA).
   */
  SubstanceUseProgram: v3_ActCode_SubstanceUseProgram,
  /**
   * Definition: A government health program that provides coverage for health services to persons meeting eligibility criteria such as income, location of residence, access to other coverages, health condition, and age, the cost of which is to some extent subsidized by public funds.
   */
  SubsidizedHealthProgram: v3_ActCode_SubsidizedHealthProgram,
  /**
   * Definition: A government health program that provides coverage through managed care contracts for health services to persons meeting eligibility criteria such as income, location of residence, access to other coverages, health condition, and age, the cost of which is to some extent subsidized by public funds.
   *
   *
   *                            Discussion: The structure and business processes for underwriting and administering a subsidized managed care program is further specified by the Underwriter and Payer Role.class and Role.code.
   */
  SubsidizedManagedCareProgram: v3_ActCode_SubsidizedManagedCareProgram,
  /**
   * Definition: A government health program that provides coverage for health services to persons meeting eligibility criteria for a supplemental health policy or program such as income, location of residence, access to other coverages, health condition, and age, the cost of which is to some extent subsidized by public funds.
   *
   *
   *                            Example:  Supplemental health coverage program may cover the cost of a health program or policy financial participations, such as the copays and the premiums, and may provide coverage for services in addition to those covered under the supplemented health program or policy.  In the U.S., Medicaid programs may pay the premium for a covered party who is also covered under the  Medicare program or a private health policy.
   *
   *
   *                            Discussion: The structure and business processes for underwriting and administering a subsidized supplemental retiree health program is further specified by the Underwriter and Payer Role.class and Role.code.
   */
  SubsidizedSupplementalHealthProgram: v3_ActCode_SubsidizedSupplementalHealthProgram,
  /**
   * Definition:
   *
   *
   *
   *
   *                               A risk or part of a risk for which there is no normal insurance market available.
   *
   *
   *
   *                               Insurance written by unauthorized insurance companies. Surplus lines insurance is insurance placed with unauthorized insurance companies through licensed surplus lines agents or brokers.
   */
  SurplusLineInsurancePolicy: v3_ActCode_SurplusLineInsurancePolicy,
  /**
   * Definition: Life insurance under which the benefit is payable only if the insured dies during a specified period. If an insured dies during that period, the beneficiary receives the death payments. If the insured survives, the policy ends and the beneficiary receives nothing.
   */
  TermLifeInsurancePolicy: v3_ActCode_TermLifeInsurancePolicy,
  /**
   * Definition: Life insurance under which the benefit is payable upon the insuredaTMs death or diagnosis of a terminal illness.  If an insured dies during that period, the beneficiary receives the death payments. If the insured survives, the policy ends and the beneficiary receives nothing
   */
  UniversalLifeInsurancePolicy: v3_ActCode_UniversalLifeInsurancePolicy,
  /**
   * Definition: A form of insurance protection that provides additional liability coverage after the limits of your underlying policy are reached. An umbrella liability policy also protects you (the insured) in many situations not covered by the usual liability policies.
   */
  UmbrellaLiabilityInsurancePolicy: v3_ActCode_UmbrellaLiabilityInsurancePolicy,
  /**
   * Definition: An automobile insurance policy under which the insurance company will indemnify a loss for which another motorist is liable if that motorist is unable to pay because he or she is uninsured.  Coverage under the policy applies to bodily injury damages only.  Injuries to the covered party caused by a hit-and-run driver are also covered.
   */
  UninsuredMotoristPolicy: v3_ActCode_UninsuredMotoristPolicy,
  /**
   * Definition: Services provided directly and through contracted and operated veteran health programs.
   */
  VeteranHealthProgram: v3_ActCode_VeteranHealthProgram,
  /**
   * Definition: Set of codes for a policy that provides coverage for health care expenses arising from vision services.
   *
   *                         A health insurance policy that covers benefits for vision care services, prescriptions, and products.
   */
  VisionCarePolicy: v3_ActCode_VisionCarePolicy,
  /**
   * Insurance policy for injuries sustained in the work place or in the course of employment.
   */
  WorkerQuoteSCompensation: v3_ActCode_WorkerQuoteSCompensation,
};
/**
 *  Domain provides codes that qualify the ActEncounterClass (ENC)
 */
export const V3ActEncounterCode = {
  /**
   * An acute inpatient encounter.
   */
  InpatientAcute: v3_ActCode_InpatientAcute,
  /**
   * A comprehensive term for health care provided in a healthcare facility (e.g. a practitioneraTMs office, clinic setting, or hospital) on a nonresident basis. The term ambulatory usually implies that the patient has come to the location and is not assigned to a bed. Sometimes referred to as an outpatient encounter.
   */
  Ambulatory: v3_ActCode_Ambulatory,
  /**
   * A patient encounter that takes place at a dedicated healthcare service delivery location where the patient receives immediate evaluation and treatment, provided until the patient can be discharged or responsibility for the patient's care is transferred elsewhere (for example, the patient could be admitted as an inpatient or transferred to another facility.)
   */
  Emergency: v3_ActCode_Emergency,
  /**
   * A patient encounter that takes place both outside a dedicated service delivery location and outside a patient's residence. Example locations might include an accident site and at a supermarket.
   */
  Field: v3_ActCode_Field,
  /**
   * Healthcare encounter that takes place in the residence of the patient or a designee
   */
  HomeHealth: v3_ActCode_HomeHealth,
  /**
   * A patient encounter where a patient is admitted by a hospital or equivalent facility, assigned to a location where patients generally stay at least overnight and provided with room, board, and continuous nursing service.
   */
  InpatientEncounter: v3_ActCode_InpatientEncounter,
  /**
   * Any category of inpatient encounter except 'acute'
   */
  InpatientNonAcute: v3_ActCode_InpatientNonAcute,
  /**
   * An encounter where the patient usually will start in different encounter, such as one in the emergency department (EMER) but then transition to this type of encounter because they require a significant period of treatment and monitoring to determine whether or not their condition warrants an inpatient admission or discharge. In the majority of cases the decision about admission or discharge will occur within a time period determined by local, regional or national regulation, often between 24 and 48 hours.
   */
  ObservationEncounter: v3_ActCode_ObservationEncounter,
  /**
   * A patient encounter where patient is scheduled or planned to receive service delivery in the future, and the patient is given a pre-admission account number. When the patient comes back for subsequent service, the pre-admission encounter is selected and is encapsulated into the service registration, and a new account number is generated.
   *
   *
   *                            Usage Note: This is intended to be used in advance of encounter types such as ambulatory, inpatient encounter, virtual, etc.
   */
  PreAdmission: v3_ActCode_PreAdmission,
  /**
   * An encounter where the patient is admitted to a health care facility for a predetermined length of time, usually less than 24 hours.
   */
  ShortStay: v3_ActCode_ShortStay,
  /**
   * A patient encounter where the patient and the practitioner(s) are not in the same physical location. Examples include telephone conference, email exchange, robotic surgery, and televideo conference.
   */
  Virtual: v3_ActCode_Virtual,
};
const v3_ActExposureLevelCode_ActExposureLevelCode: Coding = {
  code: '_ActExposureLevelCode',
  display: 'ActExposureLevelCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActExposureLevelCode',
};
const v3_ActExposureLevelCode_High: Coding = {
  code: 'HIGH',
  display: 'high',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActExposureLevelCode',
};
const v3_ActExposureLevelCode_Low: Coding = {
  code: 'LOW',
  display: 'low',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActExposureLevelCode',
};
const v3_ActExposureLevelCode_Medium: Coding = {
  code: 'MEDIUM',
  display: 'medium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActExposureLevelCode',
};
/**
 *  A qualitative measure of the degree of exposure to the causative agent.  This includes concepts such as "low", "medium" and "high".  This quantifies how the quantity that was available to be administered to the target differs from typical or background levels of the substance.
 */
export const V3ActExposureLevelCode = {
  /**
   * A qualitative measure of the degree of exposure to the causative agent.  This includes concepts such as "low", "medium" and "high".  This quantifies how the quantity that was available to be administered to the target differs from typical or background levels of the substance.
   */
  ActExposureLevelCode: v3_ActExposureLevelCode_ActExposureLevelCode,
  /**
   * Description: Exposure to an agent at a relatively high level above background.
   */
  High: v3_ActExposureLevelCode_High,
  /**
   * Description: Exposure to an agent at a relatively low level above background.
   */
  Low: v3_ActExposureLevelCode_Low,
  /**
   * Description: Exposure to an agent at a relatively moderate level above background.A
   */
  Medium: v3_ActExposureLevelCode_Medium,
};
/**
 *  Set of codes indicating the type of incident or accident.
 */
export const V3ActIncidentCode = {
  /**
   * Incident or accident as the result of a motor vehicle accident
   */
  MotorVehicleAccident: v3_ActCode_MotorVehicleAccident,
  /**
   * Incident or accident is the result of a school place accident.
   */
  SchoolAccident: v3_ActCode_SchoolAccident,
  /**
   * Incident or accident is the result of a sporting accident.
   */
  SportingAccident: v3_ActCode_SportingAccident,
  /**
   * Incident or accident is the result of a work place accident
   */
  WorkplaceAccident: v3_ActCode_WorkplaceAccident,
};
const v3_ActInvoiceElementModifier_ElectronicFormToFollow: Coding = {
  code: 'EFORM',
  display: 'Electronic Form To Follow',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActInvoiceElementModifier',
};
const v3_ActInvoiceElementModifier_FaxToFollow: Coding = {
  code: 'FAX',
  display: 'Fax To Follow',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActInvoiceElementModifier',
};
const v3_ActInvoiceElementModifier_LastInvoice: Coding = {
  code: 'LINV',
  display: 'Last Invoice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActInvoiceElementModifier',
};
const v3_ActInvoiceElementModifier_PaperDocumentationToFollow: Coding = {
  code: 'PAPER',
  display: 'Paper Documentation To Follow',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActInvoiceElementModifier',
};
/**
 *  Processing consideration and clarification codes.
 */
export const V3ActInvoiceElementModifier = {
  /**
   * Electronic form with supporting information to follow.
   */
  ElectronicFormToFollow: v3_ActInvoiceElementModifier_ElectronicFormToFollow,
  /**
   * Fax with supporting information to follow.
   */
  FaxToFollow: v3_ActInvoiceElementModifier_FaxToFollow,
  /**
   * Represents the last invoice from the perspective of the provider.
   */
  LastInvoice: v3_ActInvoiceElementModifier_LastInvoice,
  /**
   * Paper documentation (or other physical format) with supporting information to follow.
   */
  PaperDocumentationToFollow: v3_ActInvoiceElementModifier_PaperDocumentationToFollow,
};
/**
 *  Type of invoice element that is used to assist in describing an Invoice that is either submitted for adjudication or for which is returned on adjudication results. Invoice elements of this type signify a grouping of one or more children (detail) invoice elements.  They do not have intrinsic costing associated with them, but merely reflect the sum of all costing for it's immediate children invoice elements.
 */
export const V3ActInvoiceGroupCode = {
  /**
   * Type of invoice element that is used to assist in describing an Invoice that is either submitted for adjudication or for which is returned on adjudication results.
   *
   *                         Invoice elements of this type signify a grouping of one or more children (detail) invoice elements.  They do not have intrinsic costing associated with them, but merely reflect the sum of all costing for it's immediate children invoice elements.
   *
   *                         The domain is only specified for an intermediate invoice element group (non-root or non-top level) for an Invoice.
   */
  ActInvoiceInterGroupCode: v3_ActCode_ActInvoiceInterGroupCode,
  /**
   * Type of invoice element that is used to assist in describing an Invoice that is either submitted for adjudication or for which is returned on adjudication results.
   *
   *                         Invoice elements of this type signify a grouping of one or more children (detail) invoice elements.  They do not have intrinsic costing associated with them, but merely reflect the sum of all costing for it's immediate children invoice elements.
   *
   *                         Codes from this domain reflect the type of Invoice such as Pharmacy Dispense, Clinical Service and Clinical Product.  The domain is only specified for the root (top level) invoice element group for an Invoice.
   */
  ActInvoiceRootGroupCode: v3_ActCode_ActInvoiceRootGroupCode,
  /**
   * Clinical product invoice where the Invoice Grouping contains one or more billable item and is supported by clinical product(s).
   *
   *                         For example, a crutch or a wheelchair.
   */
  ClinicalProductInvoice: v3_ActCode_ClinicalProductInvoice,
  /**
   * A grouping of invoice element groups and details including the ones specifying the compound ingredients being invoiced. It may also contain generic detail items such as markup.
   */
  CompoundDrugInvoiceGroup: v3_ActCode_CompoundDrugInvoiceGroup,
  /**
   * A grouping of invoice element details including the one specifying an ingredient drug being invoiced. It may also contain generic detail items such as tax or markup.
   */
  CompoundIngredientInvoiceGroup: v3_ActCode_CompoundIngredientInvoiceGroup,
  /**
   * A grouping of invoice element groups and details including the ones specifying the compound supplies being invoiced. It may also contain generic detail items such as markup.
   */
  CompoundSupplyInvoiceGroup: v3_ActCode_CompoundSupplyInvoiceGroup,
  /**
   * Clinical Services Invoice which can be used to describe a single service, multiple services or repeated services.
   *
   *                         [1] Single Clinical services invoice where the Invoice Grouping contains one billable item and is supported by one clinical service.
   *
   *                         For example, a single service for an office visit or simple clinical procedure (e.g. knee mobilization).
   *
   *                         [2] Multiple Clinical services invoice where the Invoice Grouping contains more than one billable item, supported by one or more clinical services.  The services can be distinct and over multiple dates, but for the same patient. This type of invoice includes a series of treatments which must be adjudicated together.
   *
   *                         For example, an adjustment and ultrasound for a chiropractic session where fees are associated for each of the services and adjudicated (invoiced) together.
   *
   *                         [3] Repeated Clinical services invoice where the Invoice Grouping contains one or more billable item, supported by the same clinical service repeated over a period of time.
   *
   *                         For example, the same Chiropractic adjustment (service or treatment) delivered on 3 separate occasions over a period of time at the discretion of the provider (e.g. month).
   */
  ClinicalServiceInvoice: v3_ActCode_ClinicalServiceInvoice,
  /**
   * A clinical Invoice Grouping consisting of one or more services and one or more product.  Billing for these service(s) and product(s) are supported by multiple clinical billable events (acts).
   *
   *                         All items in the Invoice Grouping must be adjudicated together to be acceptable to the Adjudicator.
   *
   *                         For example , a brace (product) invoiced together with the fitting (service).
   */
  ClinicalServiceAndProduct: v3_ActCode_ClinicalServiceAndProduct,
  /**
   * A grouping of invoice element details including the one specifying the drug being invoiced. It may also contain generic detail items such as markup.
   */
  DrugInvoiceGroup: v3_ActCode_DrugInvoiceGroup,
  /**
   * Invoice Grouping without clinical justification.  These will not require identification of participants and associations from a clinical context such as patient and provider.
   *
   *                         Examples are interest charges and mileage.
   */
  FinancialInvoice: v3_ActCode_FinancialInvoice,
  /**
   * A grouping of invoice element details including the ones specifying the frame fee and the frame dispensing cost that are being invoiced.
   */
  FrameInvoiceGroup: v3_ActCode_FrameInvoiceGroup,
  /**
   * A grouping of invoice element details including the ones specifying the lens fee and the lens dispensing cost that are being invoiced.
   */
  LensInvoiceGroup: v3_ActCode_LensInvoiceGroup,
  /**
   * A clinical Invoice Grouping consisting of one or more oral health services. Billing for these service(s) are supported by multiple clinical billable events (acts).
   *
   *                         All items in the Invoice Grouping must be adjudicated together to be acceptable to the Adjudicator.
   */
  OralHealthService: v3_ActCode_OralHealthService,
  /**
   * HealthCare facility preferred accommodation invoice.
   */
  PreferredAccommodationInvoice: v3_ActCode_PreferredAccommodationInvoice,
  /**
   * A grouping of invoice element details including the one specifying the product (good or supply) being invoiced. It may also contain generic detail items such as tax or discount.
   */
  ProductInvoiceGroup: v3_ActCode_ProductInvoiceGroup,
  /**
   * Pharmacy dispense invoice for a compound.
   */
  RxCompoundInvoice: v3_ActCode_RxCompoundInvoice,
  /**
   * Pharmacy dispense invoice not involving a compound
   */
  RxDispenseInvoice: v3_ActCode_RxDispenseInvoice,
  /**
   * Clinical services invoice where the Invoice Group contains one billable item for multiple clinical services in one or more sessions.
   */
  SessionalOrBlockFeeInvoice: v3_ActCode_SessionalOrBlockFeeInvoice,
  /**
   * Vision dispense invoice for up to 2 lens (left and right), frame and optional discount.  Eye exams are invoiced as a clinical service invoice.
   */
  VisionDispenseInvoice: v3_ActCode_VisionDispenseInvoice,
};
const v3_ActMood_ActRequest: Coding = {
  code: '_ActMoodActRequest',
  display: 'act request',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_ActMoodCompletionTrack: Coding = {
  code: '_ActMoodCompletionTrack',
  display: 'ActMoodCompletionTrack',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Desire: Coding = {
  code: '_ActMoodDesire',
  display: 'desire',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Potential: Coding = {
  code: '_ActMoodPotential',
  display: 'potential',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_ActMoodPredicate: Coding = {
  code: '_ActMoodPredicate',
  display: 'ActMoodPredicate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Appointment: Coding = {
  code: 'APT',
  display: 'appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_AppointmentRequest: Coding = {
  code: 'ARQ',
  display: 'appointment request',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Definition: Coding = {
  code: 'DEF',
  display: 'definition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_EventOccurrence: Coding = {
  code: 'EVN',
  display: 'event (occurrence)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Intent: Coding = {
  code: 'INT',
  display: 'intent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Permission: Coding = {
  code: 'PERM',
  display: 'permission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_PermissionRequest: Coding = {
  code: 'PERMRQ',
  display: 'permission request',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Promise: Coding = {
  code: 'PRMS',
  display: 'promise',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Proposal: Coding = {
  code: 'PRP',
  display: 'proposal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Recommendation: Coding = {
  code: 'RMD',
  display: 'recommendation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_Request: Coding = {
  code: 'RQO',
  display: 'request',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
const v3_ActMood_ResourceSlot: Coding = {
  code: 'SLOT',
  display: 'resource slot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActMood',
};
/**
 *  OpenIssue:  In Ballot 2009May, a strong Negative vote was lodged against several of the concept definitions in the vocabulary used for Act.moodCode. The vote was found "Persuasive With Mod", with the understanding that M and M would undertake a detailed review of these concept definitions for a future release of the RIM.
 */
export const V3ActMood = {
  /**
   * Definition: A request (or order) for an act that is part of a defined request/fulfillment cycle.
   *
   *
   *                            UsageNotes: Use of an HL7 defined request/fulfillment framework is not required to use this mood code.
   */
  ActRequest: v3_ActMood_ActRequest,
  /**
   * These are moods describing activities as they progress in the business cycle, from defined, through planned and ordered to completed.
   */
  ActMoodCompletionTrack: v3_ActMood_ActMoodCompletionTrack,
  /**
   * Definition:  A desire to have an act occur.
   */
  Desire: v3_ActMood_Desire,
  /**
   * Definition: A possible act.
   */
  Potential: v3_ActMood_Potential,
  /**
   * Definition: An act that expresses condition statements for other acts.
   */
  ActMoodPredicate: v3_ActMood_ActMoodPredicate,
  /**
   * Definition: An act that has been scheduled to be performed at a specific place and time.
   */
  Appointment: v3_ActMood_Appointment,
  /**
   * Definition: A request act that is specialized for the appointment scheduling request/fulfillment cycle. An appointment request is fulfilled only and completely by an appointment (APT), i.e., all that the appointment request intends is to create an appointment (the actual act may well not happen if that is the professional decision during the appointment).
   */
  AppointmentRequest: v3_ActMood_AppointmentRequest,
  /**
   * Definition: A definition of a kind of act that can occur .
   *
   *
   *                            OpenIssue: The semantic constructs embodied in DEF and CRT moods seem indistinguishable, and their uses can readily be determined by the context in which these are used. Therefore, this OpenIssue has been created to declare that it is likely that ActMood.DEF will be "retired" in the future in favor of the more general ActMood.CRT.
   */
  Definition: v3_ActMood_Definition,
  /**
   * Definition: An act that actually happens (may be an ongoing act or a documentation of a past act).
   */
  EventOccurrence: v3_ActMood_EventOccurrence,
  /**
   * Definition: An act that is considered to have some noteworthy likelihood of occurring in the future (has_match = event).
   *
   *
   *                            Examples:Prognosis of a condition, Expected date of discharge from hospital, patient will likely need an emergency decompression of the intracranial pressure by morning.
   *
   *
   *                            UsageNotes:INT (intent) reflects a plan for the future, which is a declaration to do something. This contrasts with expectation, which is a prediction that something will happen in the future. GOL (goal) reflects a hope rather than a prediction. RSK (risk) reflects a potential negative event that may or may not be expected to happen.
   */
  Expectation: v3_ActMood_Expectation,
  /**
   * Definition: An expectation that is considered to be desirable to occur in the future
   *
   *
   *                            Examples:Target weight below 80Kg, Stop smoking, Regain ability to walk, goal is to administer thrombolytics to candidate patients presenting with acute myocardial infarction.
   *
   *
   *                            UsageNotes: INT (intent) reflects a plan for the future, which is a declaration to do something.  This contrasts with goal which doesn't represent an intention to act, merely a hope for an eventual result.  A goal is distinct from the intended actions to reach that goal.  "I will reduce the dose of drug x to 20mg" is an intent.  "I hope to be able to get the patient to the point where I can reduce the dose of drug x to 20mg" is a goal. EXPEC (expectation) reflects a prediction rather than a hope. RSK (risk) reflects a potential negative event rather than a hope.
   */
  Goal: v3_ActMood_Goal,
  /**
   * Definition: An intention or plan for an act.
   *
   *
   *                            >UsageNotes: The final outcome of the intent, the act that is intended to occur, is always an event. However the final outcome may be reached indirectly via steps through other intents, such as promise, permission request, or an appointment that may lead to an actual event to occur. Alternatively, the intended act may never occur.
   */
  Intent: v3_ActMood_Intent,
  /**
   * Definition: One of a set of acts that specify an option for the property values that the parent act may have. Typically used in definitions or orders to describe alternatives. An option can only be used as a group, that is, all assigned values must be used together. The actual mood of the act is the same as the parent act, and they must be linked by an actrelationship with type = OPTN.
   */
  Option: v3_ActMood_Option,
  /**
   * Definition: A kind of act that defines a permission that has been granted.
   */
  Permission: v3_ActMood_Permission,
  /**
   * Definition: A request for a permission to perform the act. Typically a payer (or possibly a supervisor) is being requested to give permission to perform the act. As opposed to the RQO, the requestee is not asked to perform or cause to perform the act but only to give the permission.
   */
  PermissionRequest: v3_ActMood_PermissionRequest,
  /**
   * Definition: A commitment to perform an act (may be either solicited or unsolicited). The committer becomes responsible to the other party for executing the act, and, as a consequence, the other party may rely on the first party to perform or cause to perform the act.
   *
   *
   *                            UsageNotes: Commitments may be retracted or cancelled.
   */
  Promise: v3_ActMood_Promise,
  /**
   * Definition: A suggestion that an act might be performed. Not an explicit request, and professional responsibility may or may not be present.
   */
  Proposal: v3_ActMood_Proposal,
  /**
   * Definition: A suggestion that an act should be performed with an acceptance of some degree of professional responsibility for the resulting act. Not an explicit request. .
   *
   *
   *                            UsageNotes: Where there is no clear definition or applicable concept of "professional responsibilityâ€?, RMD becomes indistinguishable from PRP. .
   */
  Recommendation: v3_ActMood_Recommendation,
  /**
   * Definition: A request act that is specialized for an event request/fulfillment cycle.
   *
   *
   *                            UsageNotes: The fulfillment cycle may involve intermediary fulfilling acts in moods such as PRMS, APT, or even another RQO before being fulfilled by the final event.
   *
   *
   *                            UsageNotes: The concepts of a "request" and an "order" are viewed as different, because there is an implication of a mandate associated with order.  In practice, however, this distinction has no general functional value in the inter-operation of health care computing.  "Orders" are commonly refused for a variety of clinical and business reasons, and the notion of a "request" obligates the recipient (the fulfiller) to respond to the sender (the author).  Indeed, in many regions, including Australia and Europe, the common term used is "request."
   *
   *                         Thus, the concept embodies both notions, as there is no useful distinction to be made.  If a mandate is to be associated with a request, this will be embodied in the "local" business rules applied to the transactions.  Should HL7 desire to provide a distinction between these in the future, the individual concepts could be added as specializations of this concept.
   *
   *                         The critical distinction here, is the difference between this concept and an "intent", of which it is a specialization.  An intent involves decisions by a single party, the author.  A request, however, involves decisions by two parties, the author and the fulfiller, with an obligation on the part of the fulfiller to respond to the request indicating that the fulfiller will indeed fulfill the request.
   */
  Request: v3_ActMood_Request,
  /**
   * Definition:An act that may occur in the future and which is regarded as undesirable
   *
   *
   *                            Examples:Increased risk of DVT, at risk for sub-acute bacterial endocarditis.
   *
   *
   *                            UsageNotes:Note: An observation in RSK mood expresses the undesirable act, and not the underlying risk factor. A risk factor that is present (e.g. obesity, smoking, etc) should be expressed in event mood. INT (intent) reflects a plan for the future, which is a declaration to do something. This contrasts with RSK (risk), which is the potential that something negative will occur that may or may not ever happen. GOL (goal) reflects a hope to achieve something. EXPEC (expectation) is the prediction of a positive or negative event. This contrasts with RSK (risk), which is the potential that something negative will occur that may or may not ever happen, and may not be expected to happen.
   */
  Risk: v3_ActMood_Risk,
  /**
   * Definition: A kind of act that may occur during the specified time period.
   */
  ResourceSlot: v3_ActMood_ResourceSlot,
};
/**
 *  An intention or plan to perform a service. Historical note: in previous RIM versions, the intent mood was captured as a separate class hierarchy, called Service_intent_or_order.
 */
export const V3ActMoodIntent = {
  /**
   * Definition: A request (or order) for an act that is part of a defined request/fulfillment cycle.
   *
   *
   *                            UsageNotes: Use of an HL7 defined request/fulfillment framework is not required to use this mood code.
   */
  ActRequest: v3_ActMood_ActRequest,
  /**
   * Definition:  A desire to have an act occur.
   */
  Desire: v3_ActMood_Desire,
  /**
   * Definition: An act that has been scheduled to be performed at a specific place and time.
   */
  Appointment: v3_ActMood_Appointment,
  /**
   * Definition: A request act that is specialized for the appointment scheduling request/fulfillment cycle. An appointment request is fulfilled only and completely by an appointment (APT), i.e., all that the appointment request intends is to create an appointment (the actual act may well not happen if that is the professional decision during the appointment).
   */
  AppointmentRequest: v3_ActMood_AppointmentRequest,
  /**
   * Definition: An intention or plan for an act.
   *
   *
   *                            >UsageNotes: The final outcome of the intent, the act that is intended to occur, is always an event. However the final outcome may be reached indirectly via steps through other intents, such as promise, permission request, or an appointment that may lead to an actual event to occur. Alternatively, the intended act may never occur.
   */
  Intent: v3_ActMood_Intent,
  /**
   * Definition: A request for a permission to perform the act. Typically a payer (or possibly a supervisor) is being requested to give permission to perform the act. As opposed to the RQO, the requestee is not asked to perform or cause to perform the act but only to give the permission.
   */
  PermissionRequest: v3_ActMood_PermissionRequest,
  /**
   * Definition: A commitment to perform an act (may be either solicited or unsolicited). The committer becomes responsible to the other party for executing the act, and, as a consequence, the other party may rely on the first party to perform or cause to perform the act.
   *
   *
   *                            UsageNotes: Commitments may be retracted or cancelled.
   */
  Promise: v3_ActMood_Promise,
  /**
   * Definition: A suggestion that an act might be performed. Not an explicit request, and professional responsibility may or may not be present.
   */
  Proposal: v3_ActMood_Proposal,
  /**
   * Definition: A suggestion that an act should be performed with an acceptance of some degree of professional responsibility for the resulting act. Not an explicit request. .
   *
   *
   *                            UsageNotes: Where there is no clear definition or applicable concept of "professional responsibilityâ€?, RMD becomes indistinguishable from PRP. .
   */
  Recommendation: v3_ActMood_Recommendation,
  /**
   * Definition: A request act that is specialized for an event request/fulfillment cycle.
   *
   *
   *                            UsageNotes: The fulfillment cycle may involve intermediary fulfilling acts in moods such as PRMS, APT, or even another RQO before being fulfilled by the final event.
   *
   *
   *                            UsageNotes: The concepts of a "request" and an "order" are viewed as different, because there is an implication of a mandate associated with order.  In practice, however, this distinction has no general functional value in the inter-operation of health care computing.  "Orders" are commonly refused for a variety of clinical and business reasons, and the notion of a "request" obligates the recipient (the fulfiller) to respond to the sender (the author).  Indeed, in many regions, including Australia and Europe, the common term used is "request."
   *
   *                         Thus, the concept embodies both notions, as there is no useful distinction to be made.  If a mandate is to be associated with a request, this will be embodied in the "local" business rules applied to the transactions.  Should HL7 desire to provide a distinction between these in the future, the individual concepts could be added as specializations of this concept.
   *
   *                         The critical distinction here, is the difference between this concept and an "intent", of which it is a specialization.  An intent involves decisions by a single party, the author.  A request, however, involves decisions by two parties, the author and the fulfiller, with an obligation on the part of the fulfiller to respond to the request indicating that the fulfiller will indeed fulfill the request.
   */
  Request: v3_ActMood_Request,
};
/**
 *  Any of the above service moods (e.g., event, intent, or goal) can be turned into a predicate used as a criterion to express conditionals (or queries.)  However, currently we allow only criteria on service events.
 */
export const V3ActMoodPredicate = {
  /**
   * Definition: An act that is considered to have some noteworthy likelihood of occurring in the future (has_match = event).
   *
   *
   *                            Examples:Prognosis of a condition, Expected date of discharge from hospital, patient will likely need an emergency decompression of the intracranial pressure by morning.
   *
   *
   *                            UsageNotes:INT (intent) reflects a plan for the future, which is a declaration to do something. This contrasts with expectation, which is a prediction that something will happen in the future. GOL (goal) reflects a hope rather than a prediction. RSK (risk) reflects a potential negative event that may or may not be expected to happen.
   */
  Expectation: v3_ActMood_Expectation,
  /**
   * Definition: An expectation that is considered to be desirable to occur in the future
   *
   *
   *                            Examples:Target weight below 80Kg, Stop smoking, Regain ability to walk, goal is to administer thrombolytics to candidate patients presenting with acute myocardial infarction.
   *
   *
   *                            UsageNotes: INT (intent) reflects a plan for the future, which is a declaration to do something.  This contrasts with goal which doesn't represent an intention to act, merely a hope for an eventual result.  A goal is distinct from the intended actions to reach that goal.  "I will reduce the dose of drug x to 20mg" is an intent.  "I hope to be able to get the patient to the point where I can reduce the dose of drug x to 20mg" is a goal. EXPEC (expectation) reflects a prediction rather than a hope. RSK (risk) reflects a potential negative event rather than a hope.
   */
  Goal: v3_ActMood_Goal,
  /**
   * Definition: One of a set of acts that specify an option for the property values that the parent act may have. Typically used in definitions or orders to describe alternatives. An option can only be used as a group, that is, all assigned values must be used together. The actual mood of the act is the same as the parent act, and they must be linked by an actrelationship with type = OPTN.
   */
  Option: v3_ActMood_Option,
  /**
   * Definition:An act that may occur in the future and which is regarded as undesirable
   *
   *
   *                            Examples:Increased risk of DVT, at risk for sub-acute bacterial endocarditis.
   *
   *
   *                            UsageNotes:Note: An observation in RSK mood expresses the undesirable act, and not the underlying risk factor. A risk factor that is present (e.g. obesity, smoking, etc) should be expressed in event mood. INT (intent) reflects a plan for the future, which is a declaration to do something. This contrasts with RSK (risk), which is the potential that something negative will occur that may or may not ever happen. GOL (goal) reflects a hope to achieve something. EXPEC (expectation) is the prediction of a positive or negative event. This contrasts with RSK (risk), which is the potential that something negative will occur that may or may not ever happen, and may not be expected to happen.
   */
  Risk: v3_ActMood_Risk,
};
/**
 *  Identifies types of dispensing events
 */
export const V3ActPharmacySupplyType = {
  /**
   * A fill providing sufficient supply for one day
   */
  DailyFill: v3_ActCode_DailyFill,
  /**
   * A supply action where there is no 'valid' order for the supplied medication.  E.g. Emergency vacation supply, weekend supply (when prescriber is unavailable to provide a renewal prescription)
   */
  EmergencySupply: v3_ActCode_EmergencySupply,
  /**
   * The initial fill against an order.  (This includes initial fills against refill orders.)
   */
  FirstFill: v3_ActCode_FirstFill,
  /**
   * A first fill where the quantity supplied is equal to one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a complete fill would be for the full 90 tablets).
   */
  FirstFillComplete: v3_ActCode_FirstFillComplete,
  /**
   * A first fill where the quantity supplied is less than one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a partial fill might be for only 30 tablets.)
   */
  FirstFillPartFill: v3_ActCode_FirstFillPartFill,
  /**
   * A first fill where the strength supplied is less than the ordered strength. (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  FirstFillPartialStrength: v3_ActCode_FirstFillPartialStrength,
  /**
   * A supply action to restock a smaller more local dispensary.
   */
  FloorStock: v3_ActCode_FloorStock,
  /**
   * A supply of a manufacturer sample
   */
  ManufacturerSample: v3_ActCode_ManufacturerSample,
  /**
   * A fill against an order that has already been filled (or partially filled) at least once.
   */
  Refill: v3_ActCode_Refill,
  /**
   * A refill where the quantity supplied is equal to one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a complete fill would be for the full 90 tablets.)
   */
  RefillComplete: v3_ActCode_RefillComplete,
  /**
   * A refill where the quantity supplied is equal to one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a complete fill would be for the full 90 tablets.) and where the strength supplied is less than the ordered strength (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  RefillCompletePartialStrength: v3_ActCode_RefillCompletePartialStrength,
  /**
   * The first fill against an order that has already been filled at least once at another facility.
   */
  RefillFirstFillThisFacility: v3_ActCode_RefillFirstFillThisFacility,
  /**
   * The first fill against an order that has already been filled at least once at another facility and where the strength supplied is less than the ordered strength (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  RefillPartialStrengthFirstFillThisFacility: v3_ActCode_RefillPartialStrengthFirstFillThisFacility,
  /**
   * A refill where the quantity supplied is less than one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a partial fill might be for only 30 tablets.)
   */
  RefillPartFill: v3_ActCode_RefillPartFill,
  /**
   * A refill where the quantity supplied is less than one full repetition of the ordered amount. (e.g. If the order was 90 tablets, 3 refills, a partial fill might be for only 30 tablets.) and where the strength supplied is less than the ordered strength (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  RefillPartFillPartialStrength: v3_ActCode_RefillPartFillPartialStrength,
  /**
   * A fill against an order that has already been filled (or partially filled) at least once and where the strength supplied is less than the ordered strength (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  RefillPartialStrength: v3_ActCode_RefillPartialStrength,
  /**
   * An emergency supply where the expectation is that a formal order authorizing the supply will be provided at a later date.
   */
  ScriptOwing: v3_ActCode_ScriptOwing,
  /**
   * A fill where the remainder of a 'complete' fill is provided after a trial fill has been provided.
   */
  TrialBalance: v3_ActCode_TrialBalance,
  /**
   * A fill where the remainder of a 'complete' fill is provided after a trial fill has been provided and where the strength supplied is less than the ordered strength (e.g. 10mg for an order of 50mg where a subsequent fill will dispense 40mg tablets).
   */
  TrialBalancePartialStrength: v3_ActCode_TrialBalancePartialStrength,
  /**
   * A fill where a small portion is provided to allow for determination of the therapy effectiveness and patient tolerance.
   */
  TrialFill: v3_ActCode_TrialFill,
  /**
   * A supply action that provides sufficient material for a single dose.
   */
  UnitDose: v3_ActCode_UnitDose,
  /**
   * A supply action that provides sufficient material for a single dose via multiple products.  E.g. 2 50mg tablets for a 100mg unit dose.
   */
  UnitDoseEquivalent: v3_ActCode_UnitDoseEquivalent,
};
const v3_ActPriority_ASAP: Coding = {
  code: 'A',
  display: 'ASAP',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_CallbackResults: Coding = {
  code: 'CR',
  display: 'callback results',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_CallbackForScheduling: Coding = {
  code: 'CS',
  display: 'callback for scheduling',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_CallbackPlacerForScheduling: Coding = {
  code: 'CSP',
  display: 'callback placer for scheduling',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_ContactRecipientForScheduling: Coding = {
  code: 'CSR',
  display: 'contact recipient for scheduling',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_Elective: Coding = {
  code: 'EL',
  display: 'elective',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_Emergency: Coding = {
  code: 'EM',
  display: 'emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_Preop: Coding = {
  code: 'P',
  display: 'preop',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_AsNeeded: Coding = {
  code: 'PRN',
  display: 'as needed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_Routine: Coding = {
  code: 'R',
  display: 'routine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_RushReporting: Coding = {
  code: 'RR',
  display: 'rush reporting',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_Stat: Coding = {
  code: 'S',
  display: 'stat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_TimingCritical: Coding = {
  code: 'T',
  display: 'timing critical',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_UseAsDirected: Coding = {
  code: 'UD',
  display: 'use as directed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
const v3_ActPriority_Urgent: Coding = {
  code: 'UR',
  display: 'urgent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
};
/**
 *  A set of codes (e.g., for routine, emergency), specifying the urgency under which the Act happened, can happen, is happening, is intended to happen, or is requested/demanded to happen.
 */
export const V3ActPriority = {
  /**
   * As soon as possible, next highest priority after stat.
   */
  ASAP: v3_ActPriority_ASAP,
  /**
   * Filler should contact the placer as soon as results are available, even for preliminary results.  (Was "C" in HL7 version 2.3's reporting priority.)
   */
  CallbackResults: v3_ActPriority_CallbackResults,
  /**
   * Filler should contact the placer (or target) to schedule the service.  (Was "C" in HL7 version 2.3's TQ-priority component.)
   */
  CallbackForScheduling: v3_ActPriority_CallbackForScheduling,
  /**
   * Filler should contact the placer to schedule the service.  (Was "C" in HL7 version 2.3's TQ-priority component.)
   */
  CallbackPlacerForScheduling: v3_ActPriority_CallbackPlacerForScheduling,
  /**
   * Filler should contact the service recipient (target) to schedule the service.  (Was "C" in HL7 version 2.3's TQ-priority component.)
   */
  ContactRecipientForScheduling: v3_ActPriority_ContactRecipientForScheduling,
  /**
   * Beneficial to the patient but not essential for survival.
   */
  Elective: v3_ActPriority_Elective,
  /**
   * An unforeseen combination of circumstances or the resulting state that calls for immediate action.
   */
  Emergency: v3_ActPriority_Emergency,
  /**
   * Used to indicate that a service is to be performed prior to a scheduled surgery.  When ordering a service and using the pre-op priority, a check is done to see the amount of time that must be allowed for performance of the service.  When the order is placed, a message can be generated indicating the time needed for the service so that it is not ordered in conflict with a scheduled operation.
   */
  Preop: v3_ActPriority_Preop,
  /**
   * An "as needed" order should be accompanied by a description of what constitutes a need. This description is represented by an observation service predicate as a precondition.
   */
  AsNeeded: v3_ActPriority_AsNeeded,
  /**
   * Routine service, do at usual work hours.
   */
  Routine: v3_ActPriority_Routine,
  /**
   * A report should be prepared and sent as quickly as possible.
   */
  RushReporting: v3_ActPriority_RushReporting,
  /**
   * With highest priority (e.g., emergency).
   */
  Stat: v3_ActPriority_Stat,
  /**
   * It is critical to come as close as possible to the requested time (e.g., for a through antimicrobial level).
   */
  TimingCritical: v3_ActPriority_TimingCritical,
  /**
   * Drug is to be used as directed by the prescriber.
   */
  UseAsDirected: v3_ActPriority_UseAsDirected,
  /**
   * Calls for prompt action.
   */
  Urgent: v3_ActPriority_Urgent,
};
const v3_ActReason_ActAccommodationReason: Coding = {
  code: '_ActAccommodationReason',
  display: 'ActAccommodationReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ActBillableClinicalServiceReason: Coding = {
  code: '_ActBillableClinicalServiceReason',
  display: 'ActBillableClinicalServiceReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ActBillableServiceReason: Coding = {
  code: '_ActBillableServiceReason',
  display: 'ActBillableServiceReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ActConsentInformationAccessOverrideReason: Coding = {
  code: '_ActConsentInformationAccessOverrideReason',
  display: 'ActConsentInformationAccessOverrideReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ActCoverageReason: Coding = {
  code: '_ActCoverageReason',
  display: 'ActCoverageReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ActHealthInformationManagementReason: Coding = {
  code: '_ActHealthInformationManagementReason',
  display: 'ActHealthInformationManagementReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ActIneligibilityReason: Coding = {
  code: '_ActIneligibilityReason',
  display: 'ActIneligibilityReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ActInformationManagementReason: Coding = {
  code: '_ActInformationManagementReason',
  display: 'ActInformationManagementReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ActInformationPrivacyReason: Coding = {
  code: '_ActInformationPrivacyReason',
  display: 'ActInformationPrivacyReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ActInvalidReason: Coding = {
  code: '_ActInvalidReason',
  display: 'ActInvalidReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ActInvoiceCancelReason: Coding = {
  code: '_ActInvoiceCancelReason',
  display: 'ActInvoiceCancelReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ActNoImmunizationReason: Coding = {
  code: '_ActNoImmunizationReason',
  display: 'ActNoImmunizationReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ActSupplyFulfillmentRefusalReason: Coding = {
  code: '_ActSupplyFulfillmentRefusalReason',
  display: 'ActSupplyFulfillmentRefusalReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ClinicalResearchEventReason: Coding = {
  code: '_ClinicalResearchEventReason',
  display: 'ClinicalResearchEventReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ClinicalResearchObservationReason: Coding = {
  code: '_ClinicalResearchObservationReason',
  display: 'ClinicalResearchObservationReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_CombinedPharmacyOrderSuspendReasonCode: Coding = {
  code: '_CombinedPharmacyOrderSuspendReasonCode',
  display: 'CombinedPharmacyOrderSuspendReasonCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ControlActNullificationReasonCode: Coding = {
  code: '_ControlActNullificationReasonCode',
  display: 'ControlActNullificationReasonCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ControlActNullificationRefusalReasonType: Coding = {
  code: '_ControlActNullificationRefusalReasonType',
  display: 'ControlActNullificationRefusalReasonType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ControlActReason: Coding = {
  code: '_ControlActReason',
  display: 'ControlActReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_CoverageEligibilityReason: Coding = {
  code: '_CoverageEligibilityReason',
  display: 'CoverageEligibilityReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EligibilityActReasonCode: Coding = {
  code: '_EligibilityActReasonCode',
  display: 'EligibilityActReasonCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_GenericUpdateReasonCode: Coding = {
  code: '_GenericUpdateReasonCode',
  display: 'GenericUpdateReasonCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_MedicationOrderAbortReason: Coding = {
  code: '_MedicationOrderAbortReasonCode',
  display: 'medication order abort reason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_MedicationOrderReleaseReason: Coding = {
  code: '_MedicationOrderReleaseReasonCode',
  display: 'medication order release reason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ModifyPrescriptionReasonType: Coding = {
  code: '_ModifyPrescriptionReasonType',
  display: 'ModifyPrescriptionReasonType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientProfileQueryReason: Coding = {
  code: '_PatientProfileQueryReasonCode',
  display: 'patient profile query reason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PharmacySupplyEventAbortReason: Coding = {
  code: '_PharmacySupplyEventAbortReason',
  display: 'PharmacySupplyEventAbortReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PharmacySupplyEventStockReason: Coding = {
  code: '_PharmacySupplyEventStockReasonCode',
  display: 'pharmacy supply event stock reason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PharmacySupplyRequestFulfillerRevisionRefusalReasonCode: Coding = {
  code: '_PharmacySupplyRequestFulfillerRevisionRefusalReasonCode',
  display: 'PharmacySupplyRequestFulfillerRevisionRefusalReasonCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PharmacySupplyRequestRenewalRefusalReason: Coding = {
  code: '_PharmacySupplyRequestRenewalRefusalReasonCode',
  display: 'pharmacy supply request renewal refusal reason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_RefusalReasonCode: Coding = {
  code: '_RefusalReasonCode',
  display: 'RefusalReasonCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_SchedulingActReason: Coding = {
  code: '_SchedulingActReason',
  display: 'SchedulingActReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_StatusRevisionRefusalReasonCode: Coding = {
  code: '_StatusRevisionRefusalReasonCode',
  display: 'StatusRevisionRefusalReasonCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_SubstanceAdministrationPermissionRefusalReasonCode: Coding = {
  code: '_SubstanceAdministrationPermissionRefusalReasonCode',
  display: 'SubstanceAdministrationPermissionRefusalReasonCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_SubstanceAdminSubstitutionNotAllowedReason: Coding = {
  code: '_SubstanceAdminSubstitutionNotAllowedReason',
  display: 'SubstanceAdminSubstitutionNotAllowedReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_SubstanceAdminSubstitutionReason: Coding = {
  code: '_SubstanceAdminSubstitutionReason',
  display: 'SubstanceAdminSubstitutionReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_SupplyOrderAbortReason: Coding = {
  code: '_SupplyOrderAbortReasonCode',
  display: 'supply order abort reason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_TransferActReason: Coding = {
  code: '_TransferActReason',
  display: 'TransferActReason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Accreditation: Coding = {
  code: 'ACCRED',
  display: 'accreditation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_AccommodationRequestedNotAvailable: Coding = {
  code: 'ACCREQNA',
  display: 'Accommodation Requested Not Available',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_AdministrativeErrorInOrder: Coding = {
  code: 'ADMINERROR',
  display: 'administrative error in order',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_AdministrativeReview: Coding = {
  code: 'ADMREV',
  display: 'administrative review',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_AdverseStorageCondition: Coding = {
  code: 'ADVSTORAGE',
  display: 'adverse storage condition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_AgeEligibility: Coding = {
  code: 'AGE',
  display: 'age eligibility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_AllergyIntolerance: Coding = {
  code: 'ALGINT',
  display: 'allergy intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NewPrescriptionExists: Coding = {
  code: 'ALREADYRX',
  display: 'new prescription exists',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_TryAnotherTreatmentFirst: Coding = {
  code: 'ALTCHOICE',
  display: 'try another treatment first',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_AlteredDecision: Coding = {
  code: 'ALTD',
  display: 'altered decision',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_BiomedicalResearch: Coding = {
  code: 'BIORCH',
  display: 'biomedical research',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_UnexpectedBlockOfSchedule: Coding = {
  code: 'BLK',
  display: 'Unexpected Block (of Schedule)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_BONUS: Coding = {
  code: 'BONUS',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_BreakTheGlass: Coding = {
  code: 'BTG',
  display: 'break the glass',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_CareManagement: Coding = {
  code: 'CAREMGT',
  display: 'care management',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ChildrenOnly: Coding = {
  code: 'CHD',
  display: 'Children only',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_InformationChange: Coding = {
  code: 'CHGDATA',
  display: 'information change',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PrescriptionRequiresClarification: Coding = {
  code: 'CLARIF',
  display: 'prescription requires clarification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ClinicalModification: Coding = {
  code: 'CLINMOD',
  display: 'clinical modification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ClinicalTrialResearch: Coding = {
  code: 'CLINTRCH',
  display: 'clinical trial research',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ClinicalTrialResearchWithoutPatientCare: Coding = {
  code: 'CLINTRCHNPC',
  display: 'clinical trial research without patient care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ClinicalTrialResearchWithPatientCare: Coding = {
  code: 'CLINTRCHPC',
  display: 'clinical trial research with patient care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ClinicalTrial: Coding = {
  code: 'CLINTRL',
  display: 'clinical trial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ClaimAttachment: Coding = {
  code: 'CLMATTCH',
  display: 'claim attachment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_CoordinationOfCare: Coding = {
  code: 'COC',
  display: 'coordination of care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ColdChainBreak: Coding = {
  code: 'COLDCHNBRK',
  display: 'cold chain break',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ComplianceConcern: Coding = {
  code: 'COMPCON',
  display: 'compliance concern',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Compliance: Coding = {
  code: 'COMPL',
  display: 'compliance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Contraindication: Coding = {
  code: 'CONTRA',
  display: 'contraindication',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_CoverageAuthorization: Coding = {
  code: 'COVAUTH',
  display: 'coverage authorization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_CoverageUnderPolicyOrProgram: Coding = {
  code: 'COVERAGE',
  display: 'coverage under policy or program',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_CoverageSuspended: Coding = {
  code: 'COVSUS',
  display: 'coverage suspended',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_CrimeVictim: Coding = {
  code: 'CRIME',
  display: 'crime victim',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ContinuingTherapy: Coding = {
  code: 'CT',
  display: 'continuing therapy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientDeceased: Coding = {
  code: 'DEC',
  display: 'Patient Deceased',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Deceased: Coding = {
  code: 'DECSD',
  display: 'deceased',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_DependentsOnly: Coding = {
  code: 'DEP',
  display: 'Dependents only',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Disability: Coding = {
  code: 'DIS',
  display: 'disability',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Disaster: Coding = {
  code: 'DISASTER',
  display: 'disaster',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ProductDiscontinued: Coding = {
  code: 'DISCONT',
  display: 'product discontinued',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Donation: Coding = {
  code: 'DONAT',
  display: 'donation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ChangeInMedicationDose: Coding = {
  code: 'DOSECHG',
  display: 'change in medication/dose',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_DrugLevelTooHigh: Coding = {
  code: 'DRUGHIGH',
  display: 'drug level too high',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_DiseaseSpecificHealthcareResearch: Coding = {
  code: 'DSRCH',
  display: 'disease specific healthcare research',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EmployeeAndChildren: Coding = {
  code: 'ECH',
  display: 'Employee and children',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EDU: Coding = {
  code: 'EDU',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EnteredInError: Coding = {
  code: 'EIE',
  display: 'entered in error',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EligibilityDetermination: Coding = {
  code: 'ELIGDTRM',
  display: 'eligibility determination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EligibilityVerification: Coding = {
  code: 'ELIGVER',
  display: 'eligibility verification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EmployeeOnly: Coding = {
  code: 'EMP',
  display: 'Employee only',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EmploymentBenefit: Coding = {
  code: 'EMPLOY',
  display: 'employment benefit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EntityAdministration: Coding = {
  code: 'ENADMIN',
  display: 'entity administration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Enrollment: Coding = {
  code: 'ENROLLM',
  display: 'enrollment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Error: Coding = {
  code: 'ER',
  display: 'Error',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EmergencyRoomTreatment: Coding = {
  code: 'ERTREAT',
  display: 'emergency room treatment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EmployeeAndSpouse: Coding = {
  code: 'ESP',
  display: 'Employee and spouse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EmergencyTreatment: Coding = {
  code: 'ETREAT',
  display: 'Emergency Treatment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ExpiredLot: Coding = {
  code: 'EXPLOT',
  display: 'expired lot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Family: Coding = {
  code: 'FAM',
  display: 'Family',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_FamilyPhysicianMustAuthorizeFurtherFills: Coding = {
  code: 'FAMPHYS',
  display: 'family physician must authorize further fills',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_FamilyRequested: Coding = {
  code: 'FAMRQT',
  display: 'family requested',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_FullyFilled: Coding = {
  code: 'FILLED',
  display: 'fully filled',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NoFinancialBacking: Coding = {
  code: 'FIN',
  display: 'No Financial Backing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_FinancialEligibility: Coding = {
  code: 'FINAN',
  display: 'financial eligibility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ErrorCorrection: Coding = {
  code: 'FIXDATA',
  display: 'error correction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_FloorConvenience: Coding = {
  code: 'FLRCNV',
  display: 'Floor Convenience',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_FloorStock: Coding = {
  code: 'FLRSTCK',
  display: 'floor stock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_OrderAborted: Coding = {
  code: 'FOABORT',
  display: 'order aborted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_OrderSuspended: Coding = {
  code: 'FOSUSP',
  display: 'order suspended',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_FormularyPolicy: Coding = {
  code: 'FP',
  display: 'formulary policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Fraud: Coding = {
  code: 'FRAUD',
  display: 'fraud',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_OrderStopped: Coding = {
  code: 'FRR01',
  display: 'order stopped',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_StaleDatedOrder: Coding = {
  code: 'FRR02',
  display: 'stale-dated order',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_IncompleteData: Coding = {
  code: 'FRR03',
  display: 'incomplete data',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ProductUnavailable: Coding = {
  code: 'FRR04',
  display: 'product unavailable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EthicalReligious: Coding = {
  code: 'FRR05',
  display: 'ethical/religious',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_UnableToProvideCare: Coding = {
  code: 'FRR06',
  display: 'unable to provide care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Government: Coding = {
  code: 'GOV',
  display: 'government',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_HealthAccreditation: Coding = {
  code: 'HACCRED',
  display: 'health accreditation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_HealthCompliance: Coding = {
  code: 'HCOMPL',
  display: 'health compliance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Decedent: Coding = {
  code: 'HDECD',
  display: 'decedent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Directory: Coding = {
  code: 'HDIRECT',
  display: 'directory',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_HealthcareDeliveryManagement: Coding = {
  code: 'HDM',
  display: 'healthcare delivery management',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_HealthStatus: Coding = {
  code: 'HEALTH',
  display: 'health status',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Legal: Coding = {
  code: 'HLEGAL',
  display: 'legal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_HealthcareMarketing: Coding = {
  code: 'HMARKT',
  display: 'healthcare marketing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_SuspendReasonNoLongerApplies: Coding = {
  code: 'HOLDDONE',
  display: 'suspend reason no longer applies',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_SuspendReasonInappropriate: Coding = {
  code: 'HOLDINAP',
  display: 'suspend reason inappropriate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_HealthcareOperations: Coding = {
  code: 'HOPERAT',
  display: 'healthcare operations',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_AdmissionToHospital: Coding = {
  code: 'HOSPADM',
  display: 'admission to hospital',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_HealthOutcomeMeasure: Coding = {
  code: 'HOUTCOMS',
  display: 'health outcome measure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_HealthcarePayment: Coding = {
  code: 'HPAYMT',
  display: 'healthcare payment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_HealthProgramReporting: Coding = {
  code: 'HPRGRP',
  display: 'health program reporting',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_HealthQualityImprovement: Coding = {
  code: 'HQUALIMP',
  display: 'health quality improvement',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_HealthcareResearch: Coding = {
  code: 'HRESCH',
  display: 'healthcare research',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_HealthSystemAdministration: Coding = {
  code: 'HSYSADMIN',
  display: 'health system administration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_TestHealthData: Coding = {
  code: 'HTEST',
  display: 'test health data',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ConditionImproved: Coding = {
  code: 'IMPROV',
  display: 'condition improved',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_IncorrectCoveredPartyAsPatient: Coding = {
  code: 'INCCOVPTY',
  display: 'incorrect covered party as patient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_IncorrectBilling: Coding = {
  code: 'INCINVOICE',
  display: 'incorrect billing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_IncorrectPolicy: Coding = {
  code: 'INCPOLICY',
  display: 'incorrect policy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_IncorrectProvider: Coding = {
  code: 'INCPROV',
  display: 'incorrect provider',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Individual: Coding = {
  code: 'IND',
  display: 'Individual',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Ineffective: Coding = {
  code: 'INEFFECT',
  display: 'ineffective',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_InRequestedState: Coding = {
  code: 'INRQSTATE',
  display: 'in requested state',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Intolerance: Coding = {
  code: 'INTOL',
  display: 'intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_INVOICE: Coding = {
  code: 'INVOICE',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Labeling: Coding = {
  code: 'LABELING',
  display: 'labeling',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_LabInterferenceIssues: Coding = {
  code: 'LABINT',
  display: 'lab interference issues',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Subpoena: Coding = {
  code: 'LEGAL',
  display: 'subpoena',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Locked: Coding = {
  code: 'LOCKED',
  display: 'locked',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_LongTermCareUse: Coding = {
  code: 'LTC',
  display: 'long term care use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Marketing: Coding = {
  code: 'MARKT',
  display: 'marketing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_MergeData: Coding = {
  code: 'MDATA',
  display: 'merge data',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_MedicalStatusAltered: Coding = {
  code: 'MED',
  display: 'Medical Status Altered',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_MedicalNecessity: Coding = {
  code: 'MEDNEC',
  display: 'Medical Necessity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_MemberAdministration: Coding = {
  code: 'MEMADMIN',
  display: 'member administration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_MetadataManagement: Coding = {
  code: 'METAMGT',
  display: 'metadata management',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_MilitaryCommand: Coding = {
  code: 'MILCDM',
  display: 'military command',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_MilitaryDischarge: Coding = {
  code: 'MILDCRG',
  display: 'military discharge',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ModifiedPrescriptionExists: Coding = {
  code: 'MODIFY',
  display: 'modified prescription exists',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ResponseToMonitoring: Coding = {
  code: 'MONIT',
  display: 'response to monitoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_InAnOutsideMeeting: Coding = {
  code: 'MTG',
  display: 'In an outside meeting',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_MultipleCriteriaEligibility: Coding = {
  code: 'MULTI',
  display: 'multiple criteria eligibility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientMustMakeAppointment: Coding = {
  code: 'NEEDAPMT',
  display: 'patient must make appointment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NewInformation: Coding = {
  code: 'NEWDATA',
  display: 'new information',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NewStrength: Coding = {
  code: 'NEWSTR',
  display: 'new strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NewTherapy: Coding = {
  code: 'NEWTHER',
  display: 'new therapy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NoAgentPermission: Coding = {
  code: 'NOAGNTPERM',
  display: 'no agent permission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NoMatch: Coding = {
  code: 'NOMATCH',
  display: 'no match',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientNotAvailable: Coding = {
  code: 'NON-AVAIL',
  display: 'patient not-available',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NoPermission: Coding = {
  code: 'NOPERM',
  display: 'no permission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NotPickedUp: Coding = {
  code: 'NOPICK',
  display: 'not picked up',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NoProductMatch: Coding = {
  code: 'NOPRODMTCH',
  display: 'no product match',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NoRecordMatch: Coding = {
  code: 'NORECMTCH',
  display: 'no record match',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NoLongerRequiredForTreatment: Coding = {
  code: 'NOREQ',
  display: 'no longer required for treatment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NoServiceMatch: Coding = {
  code: 'NOSERMTCH',
  display: 'no service match',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PrescriberNotAvailable: Coding = {
  code: 'NOTAVAIL',
  display: 'prescriber not available',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NotCovered: Coding = {
  code: 'NOTCOVER',
  display: 'not covered',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientNoLongerInThisPractice: Coding = {
  code: 'NOTPAT',
  display: 'patient no longer in this practice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NoUserPermission: Coding = {
  code: 'NOUSERPERM',
  display: 'no user permission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NoUserPermission_2: Coding = {
  code: 'NOUSRPERM',
  display: 'no user permission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NoVersionMatch: Coding = {
  code: 'NOVERMTCH',
  display: 'no version match',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_NonProtocol: Coding = {
  code: 'NPT',
  display: 'non-protocol',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_OfficeUse: Coding = {
  code: 'OFFICE',
  display: 'office use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_MedicationOnHold: Coding = {
  code: 'ONHOLD',
  display: 'medication on hold',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Operations: Coding = {
  code: 'OPERAT',
  display: 'operations',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_OutOfStock: Coding = {
  code: 'OS',
  display: 'out of stock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_OutcomeMeasure: Coding = {
  code: 'OUTCOMS',
  display: 'outcome measure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_AdministeredOutsideRecommendedScheduleOrPractice: Coding = {
  code: 'OUTSIDESCHED',
  display: 'administered outside recommended schedule or practice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_EmergencyTreatmentOverride: Coding = {
  code: 'OVRER',
  display: 'emergency treatment override',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_IncompetencyOverride: Coding = {
  code: 'OVRINCOMP',
  display: 'incompetency override',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ProfessionalJudgmentOverride: Coding = {
  code: 'OVRPJ',
  display: 'professional judgment override',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PublicSafetyOverride: Coding = {
  code: 'OVRPS',
  display: 'public safety override',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ThirdPartySafetyOverride: Coding = {
  code: 'OVRTPS',
  display: 'third party safety override',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientRequest: Coding = {
  code: 'PAT',
  display: 'Patient request',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientAdministration: Coding = {
  code: 'PATADMIN',
  display: 'patient administration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientCare: Coding = {
  code: 'PATCAR',
  display: 'patient care',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientChangedMind: Coding = {
  code: 'PATDEC',
  display: 'patient changed mind',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientNotEligible: Coding = {
  code: 'PATINELIG',
  display: 'patient not eligible',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientRequestQuery: Coding = {
  code: 'PATREQ',
  display: 'patient request query',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientRequested: Coding = {
  code: 'PATRQT',
  display: 'patient requested',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientSafety: Coding = {
  code: 'PATSFTY',
  display: 'patient safety',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Payment: Coding = {
  code: 'PAYMT',
  display: 'payment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PerformanceMeasure: Coding = {
  code: 'PERFMSR',
  display: 'performance measure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PharmacyTransfer: Coding = {
  code: 'PHARM',
  display: 'pharmacy transfer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PhilosophicalObjection: Coding = {
  code: 'PHILISOP',
  display: 'philosophical objection',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PhysicianRequest: Coding = {
  code: 'PHY',
  display: 'Physician request',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PropertyAndCasualtyCondition: Coding = {
  code: 'PNC',
  display: 'property and casualty condition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PopulationOriginsOrAncestryHealthcareResearch: Coding = {
  code: 'POARCH',
  display: 'population origins or ancestry healthcare research',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PopulationHealth: Coding = {
  code: 'POPHLTH',
  display: 'population health',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PerProtocol: Coding = {
  code: 'PPT',
  display: 'per protocol',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PracticeReview: Coding = {
  code: 'PRCREV',
  display: 'practice review',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PreclinicalTrialResearch: Coding = {
  code: 'PRECLINTRCH',
  display: 'preclinical trial research',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientRefuse: Coding = {
  code: 'PREFUS',
  display: 'patient refuse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ParentIsPregnantBreastFeeding: Coding = {
  code: 'PREG',
  display: 'parent is pregnant/breast feeding',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ProgramReporting: Coding = {
  code: 'PRGRPT',
  display: 'program reporting',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ProductNotAvailable: Coding = {
  code: 'PRNA',
  display: 'product not available',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PROA: Coding = {
  code: 'PROA',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ProductRecall: Coding = {
  code: 'PRODRECALL',
  display: 'product recall',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ProgramUse: Coding = {
  code: 'PROG',
  display: 'program use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ProtocolNotMet: Coding = {
  code: 'PROTUNMET',
  display: 'protocol not met',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ProviderNotAuthorized: Coding = {
  code: 'PROVUNAUTH',
  display: 'provider not authorized',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PublicHealth: Coding = {
  code: 'PUBHLTH',
  display: 'public health',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PurposeOfUse: Coding = {
  code: 'PurposeOfUse',
  display: 'purpose of use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PowerOfAttorney: Coding = {
  code: 'PWATRNY',
  display: 'power of attorney',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_QualityImprovement: Coding = {
  code: 'QUALIMP',
  display: 'quality improvement',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ChangeSupplyQuantity: Coding = {
  code: 'QUANTCHG',
  display: 'change supply quantity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ProductRecalled: Coding = {
  code: 'RECALL',
  display: 'product recalled',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_RecordsManagement: Coding = {
  code: 'RECORDMGT',
  display: 'records management',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_RECOV: Coding = {
  code: 'RECOV',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_RegisteredInError: Coding = {
  code: 'REGERR',
  display: 'registered in error',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_RegulatoryReview: Coding = {
  code: 'REGUL',
  display: 'regulatory review',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ReligiousObjection: Coding = {
  code: 'RELIG',
  display: 'religious objection',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_RemittanceAdvice: Coding = {
  code: 'REMITADV',
  display: 'remittance advice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ChangeInOrder: Coding = {
  code: 'REPLACE',
  display: 'change in order',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ErrorInOrder: Coding = {
  code: 'REPLACEFIX',
  display: 'error in order',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Research: Coding = {
  code: 'RESCH',
  display: 'research',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Retest: Coding = {
  code: 'RET',
  display: 'retest',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_RETRO: Coding = {
  code: 'RETRO',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Request: Coding = {
  code: 'RQ',
  display: 'Request',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_RegulatoryRequirement: Coding = {
  code: 'RR',
  display: 'regulatory requirement',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Research_2: Coding = {
  code: 'RSRCH',
  display: 'research',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Allergy: Coding = {
  code: 'SALG',
  display: 'allergy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Scheduled: Coding = {
  code: 'SCH',
  display: 'scheduled',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_DrugInteractsWithAnotherDrug: Coding = {
  code: 'SDDI',
  display: 'drug interacts with another drug',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_DuplicateTherapy: Coding = {
  code: 'SDUPTHER',
  display: 'duplicate therapy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_SuspectedIntolerance: Coding = {
  code: 'SINTOL',
  display: 'suspected intolerance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_SpouseAndChildren: Coding = {
  code: 'SPC',
  display: 'Spouse and children',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_SpouseOnly: Coding = {
  code: 'SPO',
  display: 'Spouse only',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Service: Coding = {
  code: 'SRVC',
  display: 'service',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_StatutoryEligibility: Coding = {
  code: 'STATUTORY',
  display: 'statutory eligibility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PrescriberStoppedMedicationForPatient: Coding = {
  code: 'STOPMED',
  display: 'prescriber stopped medication for patient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_SupportNetwork: Coding = {
  code: 'SUPNWK',
  display: 'support network',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PatientScheduledForSurgery: Coding = {
  code: 'SURG',
  display: 'patient scheduled for surgery',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_SystemAdministration: Coding = {
  code: 'SYSADMN',
  display: 'system administration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_SystemDevelopment: Coding = {
  code: 'SYSDEV',
  display: 'system development',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_TherapeuticCharacteristics: Coding = {
  code: 'THERCHAR',
  display: 'therapeutic characteristics',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Threat: Coding = {
  code: 'THREAT',
  display: 'threat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_TooEarly: Coding = {
  code: 'TOOEARLY',
  display: 'too early',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Training: Coding = {
  code: 'TRAIN',
  display: 'training',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_TRAN: Coding = {
  code: 'TRAN',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_TranslationalHealthcareResearch: Coding = {
  code: 'TRANSRCH',
  display: 'translational healthcare research',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Treatment: Coding = {
  code: 'TREAT',
  display: 'treatment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ClinicalTrialDrug: Coding = {
  code: 'TRIAL',
  display: 'clinical trial drug',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Termination: Coding = {
  code: 'TRM',
  display: 'termination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_UnmergeData: Coding = {
  code: 'UMDATA',
  display: 'unmerge data',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_UnableToUse: Coding = {
  code: 'UNABLE',
  display: 'unable to use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_UnknownTarget: Coding = {
  code: 'UNKWNTARGET',
  display: 'unknown target',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_Unscheduled: Coding = {
  code: 'UNS',
  display: 'unscheduled',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_PerDefinition: Coding = {
  code: 'UPT',
  display: 'per definition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_VaccineEfficacyConcerns: Coding = {
  code: 'VACEFF',
  display: 'vaccine efficacy concerns',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_VaccineSafetyConcerns: Coding = {
  code: 'VACSAF',
  display: 'vaccine safety concerns',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_ValidationReview: Coding = {
  code: 'VALIDATION',
  display: 'validation review',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_MotorVehicleAccidentVictim: Coding = {
  code: 'VEHIC',
  display: 'motor vehicle accident victim',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_WaitingForOldDrugToWashOut: Coding = {
  code: 'WASHOUT',
  display: 'waiting for old drug to wash out',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_WorkRelated: Coding = {
  code: 'WORK',
  display: 'work related',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
const v3_ActReason_WrongVersion: Coding = {
  code: 'WRNGVER',
  display: 'wrong version',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActReason',
};
/**
 *  A set of codes specifying the motivation, cause, or rationale of an Act, when such rationale is not reasonably represented as an ActRelationship of type "has reason" linking to another Act.  Examples:
 * Example reasons that might qualify for being coded in this field might be: "routine requirement", "infectious disease reporting requirement", "on patient request", "required by law".
 */
export const V3ActReason = {
  /**
   * Identifies the reason the patient is assigned to this accommodation type
   */
  ActAccommodationReason: v3_ActReason_ActAccommodationReason,
  /**
   * Reason for Clinical Service being performed.
   *
   *                         This domain excludes reasons specified by diagnosed conditions.
   *
   *                         Examples of values from this domain include duplicate therapy and fraudulent prescription.
   */
  ActBillableClinicalServiceReason: v3_ActReason_ActBillableClinicalServiceReason,
  /**
   * Definition: This domain is used to document reasons for providing a billable service; the billable services may include both clinical services and social services.
   */
  ActBillableServiceReason: v3_ActReason_ActBillableServiceReason,
  /**
   * To perform one or more operations on information to which the patient has not consented as deemed necessary by authorized entities for providing care in the best interest of the patient; providing immediately needed health care for an emergent condition;  or for protecting public or third party safety.
   *
   *
   *                            Usage Notes: Used to convey the reason that a provider or other entity may or has accessed personal healthcare information.  Typically, this involves overriding the subject's consent directives.
   */
  ActConsentInformationAccessOverrideReason: v3_ActReason_ActConsentInformationAccessOverrideReason,
  /**
   * Description:Codes used to specify reasons or criteria relating to coverage provided under a policy or program.  May be used to convey reasons pertaining to coverage contractual provisions, including criteria for eligibility, coverage limitations, coverage maximums, or financial participation required of covered parties.
   */
  ActCoverageReason: v3_ActReason_ActCoverageReason,
  /**
   * Description:The rationale or purpose for an act relating to health information management, such as archiving information for the purpose of complying with an organization policy or jurisdictional law relating to  data retention.
   */
  ActHealthInformationManagementReason: v3_ActReason_ActHealthInformationManagementReason,
  /**
   * Identifies the reason or rational for why a person is not eligibile for benefits under an insurance policy.
   *
   *                         Examples are client deceased & adopted client has been given a new policy identifier.
   */
  ActIneligibilityReason: v3_ActReason_ActIneligibilityReason,
  /**
   * Description:The rationale or purpose for an act relating to information management, such as archiving information for the purpose of complying with an enterprise data retention policy.
   */
  ActInformationManagementReason: v3_ActReason_ActInformationManagementReason,
  /**
   * Description:The rationale or purpose for an act relating to the management of personal information, such as disclosing personal tax information for the purpose of complying with a court order.
   */
  ActInformationPrivacyReason: v3_ActReason_ActInformationPrivacyReason,
  /**
   * Description: Types of reasons why a substance is invalid for use.
   */
  ActInvalidReason: v3_ActReason_ActInvalidReason,
  /**
   * Domain specifies the codes used to describe reasons why a Provider is cancelling an Invoice or Invoice Grouping.
   */
  ActInvoiceCancelReason: v3_ActReason_ActInvoiceCancelReason,
  /**
   * A coded description of the reason for why a patient did not receive a scheduled immunization.
   *
   *                         (important for public health strategy
   */
  ActNoImmunizationReason: v3_ActReason_ActNoImmunizationReason,
  /**
   * Indicates why a fulfiller refused to fulfill a supply order, and considered it important to notify other providers of their decision.  E.g. "Suspect fraud", "Possible abuse", "Contraindicated".
   *
   *                         (used when capturing 'refusal to fill' annotations)
   */
  ActSupplyFulfillmentRefusalReason: v3_ActReason_ActSupplyFulfillmentRefusalReason,
  /**
   * Definition:Specifies the reason that an event occurred in a clinical research study.
   */
  ClinicalResearchEventReason: v3_ActReason_ClinicalResearchEventReason,
  /**
   * Definition:SSpecifies the reason that a test was performed or observation collected in a clinical research study.
   *
   *
   *                            Note:This set of codes are not strictly reasons, but are used in the currently Normative standard.  Future revisions of the specification will model these as ActRelationships and thes codes may subsequently be retired.  Thus, these codes should not be used for new specifications.
   */
  ClinicalResearchObservationReason: v3_ActReason_ClinicalResearchObservationReason,
  /**
   * Description:Indicates why the prescription should be suspended.
   */
  CombinedPharmacyOrderSuspendReasonCode: v3_ActReason_CombinedPharmacyOrderSuspendReasonCode,
  /**
   * Description:Identifies reasons for nullifying (retracting) a particular control act.
   */
  ControlActNullificationReasonCode: v3_ActReason_ControlActNullificationReasonCode,
  /**
   * Description: Reasons to refuse a transaction to be undone.
   */
  ControlActNullificationRefusalReasonType: v3_ActReason_ControlActNullificationRefusalReasonType,
  /**
   * Identifies why a specific query, request, or other trigger event occurred.
   */
  ControlActReason: v3_ActReason_ControlActReason,
  /**
   * Definition: Identifies the reason or rational for why a person is eligibile for benefits under an insurance policy or progam.
   *
   *
   *                            Examples:  A person is a claimant under an automobile insurance policy are client deceased & adopted client has been given a new policy identifier.  A new employee is eligible for health insurance as an employment benefit.  A person meets a government program eligibility criteria for financial, age or health status.
   */
  CoverageEligibilityReason: v3_ActReason_CoverageEligibilityReason,
  /**
   * Identifies the reason or rational for why a person is eligibile for benefits under an insurance policy or progam.
   *
   *
   *                            Examples:  A person is a claimant under an automobile insurance policy are client deceased & adopted client has been given a new policy identifier.  A new employee is eligible for health insurance as an employment benefit.  A person meets a government program eligibility criteria for financial, age or health status.
   */
  EligibilityActReasonCode: v3_ActReason_EligibilityActReasonCode,
  /**
   * Description:Identifies why a change is being made to a  record.
   */
  GenericUpdateReasonCode: v3_ActReason_GenericUpdateReasonCode,
  /**
   * Description:Indicates the reason the medication order should be aborted.
   */
  MedicationOrderAbortReason: v3_ActReason_MedicationOrderAbortReason,
  /**
   * Definition:A collection of concepts that indicate why the prescription should be released from suspended state.
   */
  MedicationOrderReleaseReason: v3_ActReason_MedicationOrderReleaseReason,
  /**
   * Types of reason why a prescription is being changed.
   */
  ModifyPrescriptionReasonType: v3_ActReason_ModifyPrescriptionReasonType,
  /**
   * Definition:A collection of concepts identifying why the patient's profile is being queried.
   */
  PatientProfileQueryReason: v3_ActReason_PatientProfileQueryReason,
  /**
   * Definition:Identifies why the dispense event was not completed.
   */
  PharmacySupplyEventAbortReason: v3_ActReason_PharmacySupplyEventAbortReason,
  /**
   * Definition:A collection of concepts that indicates the reason for a "bulk supply" of medication.
   */
  PharmacySupplyEventStockReason: v3_ActReason_PharmacySupplyEventStockReason,
  /**
   * Definition:Indicates why the request to transfer a prescription from one dispensing facility to another has been refused.
   */
  PharmacySupplyRequestFulfillerRevisionRefusalReasonCode: v3_ActReason_PharmacySupplyRequestFulfillerRevisionRefusalReasonCode,
  /**
   * Definition:A collection of concepts that identifies why a renewal prescription has been refused.
   */
  PharmacySupplyRequestRenewalRefusalReason: v3_ActReason_PharmacySupplyRequestRenewalRefusalReason,
  /**
   * Description: Identifies why a request to add (or activate) a record is being refused.  Examples include the receiving system not able to match the identifier and find that record in the receiving system, having no permission, or a detected issue exists which precludes the requested action.
   */
  RefusalReasonCode: v3_ActReason_RefusalReasonCode,
  /**
   * Reasons for cancelling or rescheduling an Appointment
   */
  SchedulingActReason: v3_ActReason_SchedulingActReason,
  /**
   * Indicates why the act revision (status update) is being refused.
   */
  StatusRevisionRefusalReasonCode: v3_ActReason_StatusRevisionRefusalReasonCode,
  /**
   * Definition:Indicates why the requested authorization to prescribe or dispense a medication has been refused.
   */
  SubstanceAdministrationPermissionRefusalReasonCode: v3_ActReason_SubstanceAdministrationPermissionRefusalReasonCode,
  /**
   * Reasons why substitution of a substance administration request is not permitted.
   */
  SubstanceAdminSubstitutionNotAllowedReason: v3_ActReason_SubstanceAdminSubstitutionNotAllowedReason,
  /**
   * SubstanceAdminSubstitutionReason
   */
  SubstanceAdminSubstitutionReason: v3_ActReason_SubstanceAdminSubstitutionReason,
  /**
   * Definition:A collection of concepts that indicates why the prescription should no longer be allowed to be dispensed (but can still administer what is already being dispensed).
   */
  SupplyOrderAbortReason: v3_ActReason_SupplyOrderAbortReason,
  /**
   * The explanation for why a patient is moved from one location to another within the organization
   */
  TransferActReason: v3_ActReason_TransferActReason,
  /**
   * Description:Operational activities conducted for the purposes of meeting of criteria defined by an accrediting entity for an activity, product, or service
   */
  Accreditation: v3_ActReason_Accreditation,
  /**
   * Accommodation requested is not available.
   */
  AccommodationRequestedNotAvailable: v3_ActReason_AccommodationRequestedNotAvailable,
  /**
   * Order was created with incorrect data and is changed to reflect the intended accuracy of the order.
   */
  AdministrativeErrorInOrder: v3_ActReason_AdministrativeErrorInOrder,
  /**
   * Definition: To evaluate for service authorization, payment, reporting, or performance/outcome measures.
   */
  AdministrativeReview: v3_ActReason_AdministrativeReview,
  /**
   * Description: Storage conditions caused the substance to be ineffective.
   */
  AdverseStorageCondition: v3_ActReason_AdverseStorageCondition,
  /**
   * A person becomes eligible for a program based on age.
   *
   *
   *                            Example:  In the U.S., a person who is 65 years of age or older is eligible for Medicare.
   */
  AgeEligibility: v3_ActReason_AgeEligibility,
  /**
   * Definition: Patient has had a prior allergic intolerance response to alternate product or one of its components.
   */
  AllergyIntolerance: v3_ActReason_AllergyIntolerance,
  /**
   * Definition:Patient has already been given a new (renewal) prescription.
   */
  NewPrescriptionExists: v3_ActReason_NewPrescriptionExists,
  /**
   * Description:This therapy has been ordered as a backup to a preferred therapy.  This order will be released when and if the preferred therapy is unsuccessful.
   */
  TryAnotherTreatmentFirst: v3_ActReason_TryAnotherTreatmentFirst,
  /**
   * Description:The decision on which the recorded information was based was changed before the decision had an effect.
   *
   *
   *                            Example:Aborted prescription before patient left office, released prescription before suspend took effect.
   */
  AlteredDecision: v3_ActReason_AlteredDecision,
  /**
   * To perform one or more operations on information for conducting scientific investigations to obtain health care knowledge. Use of the data must be related to specified biomedical basic or applied research.  For example, research on rare plants to determine whether biologic properties may be useful for pharmaceutical development. May be used in combination with clinical trial and other healthcare research purposes of use.
   */
  BiomedicalResearch: v3_ActReason_BiomedicalResearch,
  /**
   * The time slots previously allocated are now blocked and no longer available for booking Appointments
   */
  UnexpectedBlockOfSchedule: v3_ActReason_UnexpectedBlockOfSchedule,
  BONUS: v3_ActReason_BONUS,
  /**
   * To perform policy override operations on information for provision of immediately needed health care for an emergent condition affecting potential harm, death or patient safety by end users who are not provisioned for this purpose of use.  Includes override of organizational provisioning policies and may include override of subject of care consent directive restricting access.
   *
   *
   *                            Map: Partially Maps to ISO 14265 Classification Term "Emergency care provision to an individual subject of care" described as "To inform persons needing to provide health care services to the subject of care urgently, possibly needing to over-ride the  policies and consents pertaining to Purpose 1 above." Purpose 1 is equivalent to HL7 treatment purpose of use: "Clinical care provision to an individual subject of care" described as "To inform persons or processes responsible for providing health care services to the subject of care."
   * The ISO description conflates both of the proposed specializations of HL7 ETREAT: break the glass and the typically broader access to health information normally available to providers who are provisioned for emergency workflows on a regular basis, e.g., Emergency Room providers. Examples of greater access than is normally accessible by providers based on the need to know are access to sensitive information for which access typically requires a patient's consent.  This is not an override of a patient's dissent to disclose sensitive information in cases where the applicable policy waives the need for that consent to access this information. In US, Title 38 Section 7332 and 42 CFR Part 2 both permit emergency access without the need to override a patient's consent directive; rather, this access is a limitation to the patient's right to dissent from disclosure.
   */
  BreakTheGlass: v3_ActReason_BreakTheGlass,
  /**
   * To perform analytics, evaluation and other secondary uses of treatment and healthcare related information to manage the quality, efficacy, patient safety, population health, and cost effectiveness of healthcare delivery. Explicitly excludes the use of information to organize the delivery of health care for care coordination and case management, or to provide healthcare treatment.
   *
   *
   *                            Usage Note: The concept of care management is narrower than the list of activities related to more general organizational objectives such as provider profiling, education of healthcare and non-healthcare professionals; insurance underwriting, premium rating, reinsurance; organizational legal, medical review, auditing, compliance and fraud and abuse detection; business planning, development, and restructuring; fund-raising; and customer service.
   *
   *
   *                            Map: Maps to ISO 14265 Classification Term "Health service management and quality assurance" described as "To inform persons or processes responsible for determining the availability, quality, safety, equity and cost-effectiveness of health care services."
   *
   *                         There is a semantic gap in concepts.  This classification term  is described as activities, i.e., "to inform persons" or "to inform processes" rather than the rationale for performing actions/operations on information related to the activity.
   */
  CareManagement: v3_ActReason_CareManagement,
  /**
   * Description:The level of coverage under the policy or program is available only to children
   */
  ChildrenOnly: v3_ActReason_ChildrenOnly,
  /**
   * Description:Information has changed since the record was created.
   */
  InformationChange: v3_ActReason_InformationChange,
  /**
   * Description:Clarification is required before the order can be acted upon.
   */
  PrescriptionRequiresClarification: v3_ActReason_PrescriptionRequiresClarification,
  /**
   * Order is changed based on a clinical reason.
   */
  ClinicalModification: v3_ActReason_ClinicalModification,
  /**
   * To perform one or more operations on information for conducting scientific investigations in accordance with clinical trial protocols to obtain health care knowledge.
   */
  ClinicalTrialResearch: v3_ActReason_ClinicalTrialResearch,
  /**
   * To perform one or more operations on information for conducting scientific investigations in accordance with clinical trial protocols to obtain health care knowledge without provision of patient care. May be post-coordinated or used with other purposes of use such as disease, discipline, specialty, population origins or ancestry, translational healthcare research. For example, a clinical trial conducted on laboratory specimens collected from a specified patient population.
   */
  ClinicalTrialResearchWithoutPatientCare: v3_ActReason_ClinicalTrialResearchWithoutPatientCare,
  /**
   * To perform one or more operations on information for conducting scientific investigations with patient care in accordance with clinical trial protocols to obtain health care knowledge. May be post-coordinated or used with other purposes of use such as disease, discipline, specialty, population origins or ancestry, translational healthcare research. For example, an "off-label" drug used for cancer therapy administer to a specified patient population.
   */
  ClinicalTrialResearchWithPatientCare: v3_ActReason_ClinicalTrialResearchWithPatientCare,
  /**
   * To perform health care as part of the clinical trial protocol.
   */
  ClinicalTrial: v3_ActReason_ClinicalTrial,
  /**
   * To perform one or more operations on information for provision of additional clinical evidence in support of a request for coverage or payment for health services.
   */
  ClaimAttachment: v3_ActReason_ClaimAttachment,
  /**
   * To perform one or more actions on information in order to organize the provision and case management of an individualâ€™s healthcare, including: Monitoring a person's goals, needs, and preferences; acting as the communication link between two or more participants concerned with a person's health and wellness; organizing and facilitating care activities and promoting self-management by advocating for, empowering, and educating a person; and ensuring safe, appropriate, non-duplicative, and effective integrated care.
   *
   *
   *                            Usage Note: Use when describing these functions: 1. Monitoring a personâ€™s goals, needs, and preferences.   2. Acting as the communication link between two or more participants concerned with a person's health and wellness.  3. Organizing and facilitating care activities and promoting self-management by advocating for, empowering, and educating a person.  4. Ensuring safe, appropriate, non-duplicative, and effective integrated care.
   *
   *                         The goal is to clearly differentiate this type of coordination of care from HIPAA Operations by specifying that these actions on information are undertaken in the provision of healthcare treatment.
   *
   *                         For similar uses of this concept, see SAMHSA Confidentiality of Substance Use Disorder Patient Records Supplemental notice of proposed rulemaking, which differentiates concepts of care coordination and case management for the provision of treatment as specifically distinct from activities related to health care delivery management and the operations of organizational entities involved in the delivery of healthcare.
   *
   *
   *                            Map: Maps to ISO 14265 Classification Terms: "Support of care activities within the provider organisation for an individual subject of care" described as "To inform persons or processes enabling others to provide health care services to the subject of care."  "Subject of Care Uses" described as "To inform the subject of care in support of his or her own interests."
   */
  CoordinationOfCare: v3_ActReason_CoordinationOfCare,
  /**
   * Description: Cold chain was not maintained for the substance.
   */
  ColdChainBreak: v3_ActReason_ColdChainBreak,
  /**
   * Definition: Patient has compliance issues with medication such as differing appearance, flavor, size, shape or consistency.
   */
  ComplianceConcern: v3_ActReason_ComplianceConcern,
  /**
   * Description:Operational activities required to meet a mandate related to an activity, product, or service
   */
  Compliance: v3_ActReason_Compliance,
  /**
   * Definition:Contraindication identified
   */
  Contraindication: v3_ActReason_Contraindication,
  /**
   * To perform one or more operations on information for conducting prior authorization or predetermination of coverage for services.
   */
  CoverageAuthorization: v3_ActReason_CoverageAuthorization,
  /**
   * To perform one or more operations on information for conducting activities related to coverage under a program or policy.
   */
  CoverageUnderPolicyOrProgram: v3_ActReason_CoverageUnderPolicyOrProgram,
  /**
   * When a client has no contact with the health system for an extended period, coverage is suspended.  Client will be reinstated to original start date upon proof of identification, residency etc.
   *
   *                         Example: Coverage may be suspended during a strike situation, when employer benefits for employees are not covered (i.e. not in effect).
   */
  CoverageSuspended: v3_ActReason_CoverageSuspended,
  /**
   * A person becomes eligible for insurance or a program because of crime related health condition or injury.
   *
   *
   *                            Example:  A person is a claimant under the U.S. Crime Victims Compensation program.
   */
  CrimeVictim: v3_ActReason_CrimeVictim,
  /**
   * Indicates that the decision to substitute or to not substitute was driven by a desire to maintain consistency with a pre-existing therapy.  I.e. The performer provided the same item/service as had been previously provided rather than providing exactly what was ordered, or rather than substituting with a lower-cost equivalent.
   */
  ContinuingTherapy: v3_ActReason_ContinuingTherapy,
  /**
   * The Patient is deceased
   */
  PatientDeceased: v3_ActReason_PatientDeceased,
  /**
   * Client deceased.
   */
  Deceased: v3_ActReason_Deceased,
  /**
   * Description:The level of coverage under the policy or program is available only to a subscriber's dependents.
   */
  DependentsOnly: v3_ActReason_DependentsOnly,
  /**
   * A person becomes a claimant under a disability income insurance policy or a disability rehabilitation program because of a health condition or injury which limits the person's ability to earn an income or function without institutionalization.
   */
  Disability: v3_ActReason_Disability,
  /**
   * To perform one or more operations on information used for provision of immediately needed health care to a population of living subjects located in a disaster zone.
   */
  Disaster: v3_ActReason_Disaster,
  /**
   * Description:The medication is no longer being manufactured or is otherwise no longer available.
   */
  ProductDiscontinued: v3_ActReason_ProductDiscontinued,
  /**
   * To perform one or more operations on information used for cadaveric organ, eye or tissue donation.
   */
  Donation: v3_ActReason_Donation,
  /**
   * Description:The medication is being re-prescribed at a different dosage.
   */
  ChangeInMedicationDose: v3_ActReason_ChangeInMedicationDose,
  /**
   * Description:The current level of the medication in the patient's system is too high.  The medication is suspended to allow the level to subside to a safer level.
   */
  DrugLevelTooHigh: v3_ActReason_DrugLevelTooHigh,
  /**
   * To perform one or more operations on information for conducting scientific investigations to obtain health care knowledge. Use of the data must be related to specified conditions, diagnosis, or disease healthcare research.  For example, conducting cancer research by testing reaction of tumor cells to certain biologics. May be used in combination with clinical trial and other healthcare research purposes of use.
   */
  DiseaseSpecificHealthcareResearch: v3_ActReason_DiseaseSpecificHealthcareResearch,
  /**
   * Description:The level of coverage under the policy or program is available to an employee and his or her children.
   */
  EmployeeAndChildren: v3_ActReason_EmployeeAndChildren,
  EDU: v3_ActReason_EDU,
  /**
   * Description:The information was recorded incorrectly or was recorded in the wrong record.
   */
  EnteredInError: v3_ActReason_EnteredInError,
  /**
   * To perform one or more operations on information used for conducting eligibility determination for coverage in a program or policy.  May entail review of financial status or disability assessment.
   */
  EligibilityDetermination: v3_ActReason_EligibilityDetermination,
  /**
   * To perform one or more operations on information used for conducting eligibility verification of coverage in a program or policy.  May entail provider contacting coverage source (e.g., government health program such as workers compensation or health plan) for confirmation of enrollment, eligibility for specific services, and any applicable copays.
   */
  EligibilityVerification: v3_ActReason_EligibilityVerification,
  /**
   * Description:The level of coverage under the policy or program is available only to an employee.
   */
  EmployeeOnly: v3_ActReason_EmployeeOnly,
  /**
   * A person becomes eligible for insurance provided as an employment benefit based on employment status.
   */
  EmploymentBenefit: v3_ActReason_EmploymentBenefit,
  /**
   * Description:Operational activities conducted to administer information relating to entities involves with an activity, product, or service
   */
  EntityAdministration: v3_ActReason_EntityAdministration,
  /**
   * To perform one or more operations on information used for enrolling a covered party in a program or policy.  May entail recording of covered party's and any dependent's demographic information and benefit choices.
   */
  Enrollment: v3_ActReason_Enrollment,
  /**
   * Moved to an error in placing the patient in the original location.
   */
  Error: v3_ActReason_Error,
  /**
   * To perform one or more operations on information for provision of immediately needed health care for an emergent condition in an emergency room or similar emergent care context by end users provisioned for this purpose, which does not constitute as policy override such as in a "Break the Glass" purpose of use.
   *
   *                         Map:Partially Maps to ISO 14265 Classification Term "Emergency care provision to an individual subject of care" described as "To inform persons needing to provide health care services to the subject of care urgently, possibly needing to over-ride the  policies and consents pertaining to Purpose 1 above." Purpose 1 is equivalent to HL7 treatment purpose of use: "Clinical care provision to an individual subject of care" described as "To inform persons or processes responsible for providing health care services to the subject of care."
   *
   *                         The ISO description conflates both of the proposed specializations of HL7 ETREAT: break the glass and the typically broader access to health information normally available to providers who are provisioned for emergency workflows on a regular basis, e.g., Emergency Room providers. Examples of greater access than is normally accessible by providers based on the need to know are access to sensitive information for which access typically requires a patient's consent.  This is not an override of a patient's dissent to disclose sensitive information in cases where the applicable policy waives the need for that consent to access this information. In US, Title 38 Section 7332 and 42 CFR Part 2 both permit emergency access without the need to override a patient's consent directive; rather, this access is a limitation to the patient's right to dissent from disclosure.
   *
   *                         There is a semantic gap in concepts.  This classification term is described as activities â€œto inform personsâ€? rather than the rationale for performing actions/operations on information related to the activity.
   */
  EmergencyRoomTreatment: v3_ActReason_EmergencyRoomTreatment,
  /**
   * Description:The level of coverage under the policy or program is available to an employee and his or her spouse.
   */
  EmployeeAndSpouse: v3_ActReason_EmployeeAndSpouse,
  /**
   * To perform one or more operations on information for provision of immediately needed health care for an emergent condition.
   */
  EmergencyTreatment: v3_ActReason_EmergencyTreatment,
  /**
   * Description: The lot from which the substance was drawn was expired.
   */
  ExpiredLot: v3_ActReason_ExpiredLot,
  /**
   * Description:The level of coverage under the policy or program is available to a subscriber's family.
   */
  Family: v3_ActReason_Family,
  /**
   * Definition:Request for further authorization must be done through patient's family physician.
   */
  FamilyPhysicianMustAuthorizeFurtherFills: v3_ActReason_FamilyPhysicianMustAuthorizeFurtherFills,
  /**
   * To perform one or more operations on information in response to a request by a family member authorized by the patient.
   */
  FamilyRequested: v3_ActReason_FamilyRequested,
  /**
   * Ordered quantity has already been completely fulfilled.
   */
  FullyFilled: v3_ActReason_FullyFilled,
  /**
   * Patient unable to pay and not covered by insurance
   */
  NoFinancialBacking: v3_ActReason_NoFinancialBacking,
  /**
   * A person becomes eligible for a program based on financial criteria.
   *
   *
   *                            Example:  A person whose family income is below a financial threshold for eligibility for Medicaid or SCHIP.
   */
  FinancialEligibility: v3_ActReason_FinancialEligibility,
  /**
   * Description:Previously recorded information was erroneous and is being corrected.
   */
  ErrorCorrection: v3_ActReason_ErrorCorrection,
  /**
   * Accommodation is assigned for floor convenience.
   */
  FloorConvenience: v3_ActReason_FloorConvenience,
  /**
   * Definition:The bulk supply is issued to replenish a ward for local dispensing.  (Includes both mobile and fixed-location ward stocks.)
   */
  FloorStock: v3_ActReason_FloorStock,
  /**
   * Definition:Order to be fulfilled was aborted
   */
  OrderAborted: v3_ActReason_OrderAborted,
  /**
   * Definition:Order to be fulfilled was suspended
   */
  OrderSuspended: v3_ActReason_OrderSuspended,
  /**
   * Indicates that the decision to substitute or to not substitute was driven by a policy expressed within the formulary.
   */
  FormularyPolicy: v3_ActReason_FormularyPolicy,
  /**
   * To perform one or more operations on information used for fraud detection and prevention processes.
   */
  Fraud: v3_ActReason_Fraud,
  /**
   * Definition:The order has been stopped by the prescriber but this fact has not necessarily captured electronically.
   *
   *
   *                            Example:A verbal stop, a fax, etc.
   */
  OrderStopped: v3_ActReason_OrderStopped,
  /**
   * Definition:Order has not been fulfilled within a reasonable amount of time, and may not be current.
   */
  StaleDatedOrder: v3_ActReason_StaleDatedOrder,
  /**
   * Definition:Data needed to safely act on the order which was expected to become available independent of the order is not yet available
   *
   *
   *                            Example:Lab results, diagnostic imaging, etc.
   */
  IncompleteData: v3_ActReason_IncompleteData,
  /**
   * Definition:Product not available or manufactured. Cannot supply.
   */
  ProductUnavailable: v3_ActReason_ProductUnavailable,
  /**
   * Definition:The dispenser has ethical, religious or moral objections to fulfilling the order/dispensing the product.
   */
  EthicalReligious: v3_ActReason_EthicalReligious,
  /**
   * Definition:Fulfiller not able to provide appropriate care associated with fulfilling the order.
   *
   *
   *                            Example:Therapy requires ongoing monitoring by fulfiller and fulfiller will be ending practice, leaving town, unable to schedule necessary time, etc.
   */
  UnableToProvideCare: v3_ActReason_UnableToProvideCare,
  /**
   * To perform one or more operations on information used within government processes.
   */
  Government: v3_ActReason_Government,
  /**
   * To perform one or more operations on information for conducting activities related to meeting accreditation criteria.
   */
  HealthAccreditation: v3_ActReason_HealthAccreditation,
  /**
   * To perform one or more operations on information used for conducting activities required to meet a mandate.
   */
  HealthCompliance: v3_ActReason_HealthCompliance,
  /**
   * To perform one or more operations on information used for handling deceased patient matters.
   */
  Decedent: v3_ActReason_Decedent,
  /**
   * To perform one or more operation operations on information used to manage a patient directory.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            facility
   *                            enterprise
   *                            payer
   *                            health information exchange patient directory
   */
  Directory: v3_ActReason_Directory,
  /**
   * To perform one or more actions on information used for conducting administrative and contractual activities by or on behalf of organizational entities responsible for delivery of  an individual's benefits in a healthcare program, health plan or insurance.   Explicitly excludes the use of information to organize the delivery of health care for care coordination and case management, or to provide healthcare treatment.
   *
   *
   *                            Usage Note: Examples of activities conducted under this purpose of use: provider profiling, risk adjustment, underwriting, fraud and abuse, quality improvement population health and care management. Aligns with HIPAA Operation POU minus coordination of care or other treatment related activities. Similar to the description in SAMHSA Confidentiality of Substance Use Disorder Patient Records Supplemental notice of proposed rulemaking.
   *
   *
   *                            Map: Maps to ISO 14265 Classification Term  "Administration of care for an individual subject of care" described as "To inform persons or processes responsible for enabling the availability of resources or funding or permissions for providing health care services to the subject of care."
   *
   *                         However, this classification term is described as activities, i.e., "to inform persons" or "to inform processes" rather than the rationale for performing actions/operations on information related to the activity.
   */
  HealthcareDeliveryManagement: v3_ActReason_HealthcareDeliveryManagement,
  /**
   * A person becomes eligible for a program because of a qualifying health condition or injury.
   *
   *
   *                            Examples:  A person is determined to have a qualifying health conditions include pregnancy, HIV/AIDs, tuberculosis, end stage renal disease, breast or cervical cancer, or other condition requiring specialized health services, hospice, institutional or community based care provided under a program
   */
  HealthStatus: v3_ActReason_HealthStatus,
  /**
   * To perform one or more operations on information for conducting activities required by legal proceeding.
   */
  Legal: v3_ActReason_Legal,
  /**
   * To perform one or more operations on information for marketing services and products related to health care.
   */
  HealthcareMarketing: v3_ActReason_HealthcareMarketing,
  /**
   * Definition:The original reason for suspending the medication has ended.
   */
  SuspendReasonNoLongerApplies: v3_ActReason_SuspendReasonNoLongerApplies,
  /**
   * Definition:
   */
  SuspendReasonInappropriate: v3_ActReason_SuspendReasonInappropriate,
  /**
   * To perform one or more operations on information used for conducting administrative and contractual activities related to the provision of health care.
   */
  HealthcareOperations: v3_ActReason_HealthcareOperations,
  /**
   * Description:The patient has been admitted to a care facility and their community medications are suspended until hospital discharge.
   */
  AdmissionToHospital: v3_ActReason_AdmissionToHospital,
  /**
   * To perform one or more operations on information used for assessing results and comparative effectiveness achieved by health care practices and interventions.
   */
  HealthOutcomeMeasure: v3_ActReason_HealthOutcomeMeasure,
  /**
   * To perform one or more operations on information for conducting financial or contractual activities related to payment for provision of health care.
   */
  HealthcarePayment: v3_ActReason_HealthcarePayment,
  /**
   * To perform one or more operations on information used for conducting activities to meet program accounting requirements.
   */
  HealthProgramReporting: v3_ActReason_HealthProgramReporting,
  /**
   * To perform one or more operations on information used for conducting administrative activities to improve health care quality.
   */
  HealthQualityImprovement: v3_ActReason_HealthQualityImprovement,
  /**
   * To perform one or more operations on information for conducting scientific investigations to obtain health care knowledge.  Use of the data iincludes basic and applied research such as biomedical, population origin or ancestry, translational research, and disease, discipline, specialty specific healthcare research and clinical trial research.
   */
  HealthcareResearch: v3_ActReason_HealthcareResearch,
  /**
   * To perform one or more operations on information to administer the electronic systems used for the delivery of health care.
   */
  HealthSystemAdministration: v3_ActReason_HealthSystemAdministration,
  /**
   * To perform one or more operations on information that is simulated or synthetic health data used for testing system capabilities outside of a production or operational system environment.
   *
   *
   *                            Usage Note: Data marked with a HTEST security label enables an access control system to permit interfacing systems or end users provisioned with a clearance, which includes a HTEST purpose of use attribute, to test, verify, or validate that a system or application will operate in production as intended based on design specifications.
   */
  TestHealthData: v3_ActReason_TestHealthData,
  /**
   * Definition:Testing has shown that the patient already has immunity to the agent targeted by the immunization.
   */
  Immunity: v3_ActReason_Immunity,
  /**
   * Definition:The patient's medical condition has nearly abated.
   */
  ConditionImproved: v3_ActReason_ConditionImproved,
  /**
   * The covered party (patient) specified with the Invoice is not correct.
   */
  IncorrectCoveredPartyAsPatient: v3_ActReason_IncorrectCoveredPartyAsPatient,
  /**
   * The billing information, specified in the Invoice Elements, is not correct.  This could include incorrect costing for items included in the Invoice.
   */
  IncorrectBilling: v3_ActReason_IncorrectBilling,
  /**
   * The policy specified with the Invoice is not correct.  For example, it may belong to another Adjudicator or Covered Party.
   */
  IncorrectPolicy: v3_ActReason_IncorrectPolicy,
  /**
   * The provider specified with the Invoice is not correct.
   */
  IncorrectProvider: v3_ActReason_IncorrectProvider,
  /**
   * Description:The level of coverage under the policy or program is available to an individual.
   */
  Individual: v3_ActReason_Individual,
  /**
   * Description:The therapy has been found to not have the desired therapeutic benefit on the patient.
   */
  Ineffective: v3_ActReason_Ineffective,
  /**
   * The record is already in the requested state.
   */
  InRequestedState: v3_ActReason_InRequestedState,
  /**
   * Description:The patient has an intolerance to the medication.
   */
  Intolerance: v3_ActReason_Intolerance,
  INVOICE: v3_ActReason_INVOICE,
  /**
   * To perform one or more operations on information to assign, persist, and manage labels to healthcare data to characterize various aspects, such as its security classification, sensitivity, compartment, integrity, and provenance; applicable privacy, consent, security, provenance, and trust policies; and handling caveats such as purpose of use, obligations, and refrain policies.
   *
   *                         Label management includes classification of target data by constructing and binding of a label set per applicable policies, security policy information file semantics, and classification guides.  Label management also includes process and procedures for subsequent revision of a label for, e.g., reclassification, downgrading classification, and declassification.
   *
   *                         Label revisions may be triggered by, e.g., expiry of classification period; changes in applicable policy, e.g., revocation of a consent directive; or changes in the governing policy domain in which the data is relocated or a copy of the data is sent.  If a label is revised, an audit log should be kept and the provenance of the label changes should be tracked.
   */
  Labeling: v3_ActReason_Labeling,
  /**
   * Description:The therapy would interfere with a planned lab test and the therapy is being withdrawn until the test is completed.
   */
  LabInterferenceIssues: v3_ActReason_LabInterferenceIssues,
  /**
   * Definition:To provide information as a result of a subpoena.
   */
  Subpoena: v3_ActReason_Subpoena,
  /**
   * Definition:The prescription may not be reassigned from the original pharmacy.
   */
  Locked: v3_ActReason_Locked,
  /**
   * Definition:The bulk supply will be administered within a long term care facility.
   */
  LongTermCareUse: v3_ActReason_LongTermCareUse,
  /**
   * Description:
   */
  Marketing: v3_ActReason_Marketing,
  /**
   * Information is combined into the record.
   */
  MergeData: v3_ActReason_MergeData,
  /**
   * The medical condition of the Patient has changed
   */
  MedicalStatusAltered: v3_ActReason_MedicalStatusAltered,
  /**
   * Required for medical reasons(s).
   */
  MedicalNecessity: v3_ActReason_MedicalNecessity,
  /**
   * Definition:The patient currently has a medical condition for which the vaccine is contraindicated or for which precaution is warranted.
   */
  MedicalPrecaution: v3_ActReason_MedicalPrecaution,
  /**
   * To perform one or more operations on information to administer health care coverage to an enrollee under a policy or program.
   */
  MemberAdministration: v3_ActReason_MemberAdministration,
  /**
   * To perform one or more operations on information to assign, persist, and manage metadata to healthcare data to characterize various aspects used for its indexing, discovery, retrieval, and processing by systems, applications, and end users.  For example, master index identifier, media type, and location.
   */
  MetadataManagement: v3_ActReason_MetadataManagement,
  /**
   * To perform one or more operations on information for conducting activities required by military processes, procedures, policies, or law.
   */
  MilitaryCommand: v3_ActReason_MilitaryCommand,
  /**
   * To perform one or more operations on information for the process of releasing military personnel from their service obligations, which may include determining service merit, discharge benefits, and disability assessment.
   */
  MilitaryDischarge: v3_ActReason_MilitaryDischarge,
  /**
   * Definition:Therapy has been changed and new prescription issued
   */
  ModifiedPrescriptionExists: v3_ActReason_ModifiedPrescriptionExists,
  /**
   * Description:Monitoring the patient while taking the medication, the decision has been made that the therapy is no longer appropriate.
   */
  ResponseToMonitoring: v3_ActReason_ResponseToMonitoring,
  /**
   * The Physician is in a meeting.  For example, he/she may request administrative time to talk to family after appointment
   */
  InAnOutsideMeeting: v3_ActReason_InAnOutsideMeeting,
  /**
   * A person becomes eligible for a program based on more than one criterion.
   *
   *
   *                            Examples:  In the U.S., a child whose familiy income meets Medicaid financial thresholds and whose age is less than 18 is eligible for the Early and Periodic Screening, Diagnostic, and Treatment program (EPSDT).  A person whose family income meets Medicaid financial thresholds and whose age is 65 years or older is eligible for Medicaid and Medicare, and are referred to as dual eligibles.
   */
  MultipleCriteriaEligibility: v3_ActReason_MultipleCriteriaEligibility,
  /**
   * Definition:Patient must see prescriber prior to further fills.
   */
  PatientMustMakeAppointment: v3_ActReason_PatientMustMakeAppointment,
  /**
   * Description:New information has become available to supplement the record.
   */
  NewInformation: v3_ActReason_NewInformation,
  /**
   * Definition:The current medication will be replaced by a new strength of the same medication.
   */
  NewStrength: v3_ActReason_NewStrength,
  /**
   * Definition:A new therapy will be commenced when current supply exhausted.
   */
  NewTherapy: v3_ActReason_NewTherapy,
  /**
   * Description: The agent does not have permission.
   */
  NoAgentPermission: v3_ActReason_NoAgentPermission,
  /**
   * Description: There is no match.
   */
  NoMatch: v3_ActReason_NoMatch,
  /**
   * Description:Patient not available for a period of time due to a scheduled therapy, leave of absence or other reason.
   */
  PatientNotAvailable: v3_ActReason_PatientNotAvailable,
  /**
   * Description: There is no permission.
   */
  NoPermission: v3_ActReason_NoPermission,
  /**
   * Definition:Patient did not come to get medication
   */
  NotPickedUp: v3_ActReason_NotPickedUp,
  /**
   * Description: There is no match for the product in the master file repository.
   */
  NoProductMatch: v3_ActReason_NoProductMatch,
  /**
   * Description: There is no match for the record in the database.
   */
  NoRecordMatch: v3_ActReason_NoRecordMatch,
  /**
   * Description:The underlying condition has been resolved or has evolved such that a different treatment is no longer needed.
   */
  NoLongerRequiredForTreatment: v3_ActReason_NoLongerRequiredForTreatment,
  /**
   * Description: There is no match for the service in the master file repository.
   */
  NoServiceMatch: v3_ActReason_NoServiceMatch,
  /**
   * Definition:Original prescriber is no longer available to prescribe and no other prescriber has taken responsibility for the patient.
   */
  PrescriberNotAvailable: v3_ActReason_PrescriberNotAvailable,
  /**
   * Description:The product does not have (or no longer has) coverage under the patientaTMs insurance policy.
   */
  NotCovered: v3_ActReason_NotCovered,
  /**
   * Definition:Patient no longer or has never been under this prescribers care.
   */
  PatientNoLongerInThisPractice: v3_ActReason_PatientNoLongerInThisPractice,
  /**
   * Definition:The user does not have permission
   */
  NoUserPermission: v3_ActReason_NoUserPermission,
  /**
   * Description: The user does not have permission.
   */
  NoUserPermission_2: v3_ActReason_NoUserPermission_2,
  /**
   * Description: There is no match for the record and version.
   */
  NoVersionMatch: v3_ActReason_NoVersionMatch,
  /**
   * Definition:The observation or test was neither defined or scheduled in the study protocol.
   */
  NonProtocol: v3_ActReason_NonProtocol,
  /**
   * Definition:The bulk supply is intended for general clinician office use.
   */
  OfficeUse: v3_ActReason_OfficeUse,
  /**
   * Definition:This medication is on hold.
   */
  MedicationOnHold: v3_ActReason_MedicationOnHold,
  /**
   * Description:Administrative and contractual processes required to support an activity, product, or service
   */
  Operations: v3_ActReason_Operations,
  /**
   * In the case of 'substitution', indicates that the substitution occurred because the ordered item was not in stock.  In the case of 'no substitution', indicates that a cheaper equivalent was not substituted because it was not in stock.
   */
  OutOfStock: v3_ActReason_OutOfStock,
  /**
   * Definition:There was no supply of the product on hand to perform the service.
   */
  ProductOutOfStock: v3_ActReason_ProductOutOfStock,
  /**
   * Description:Operational activities conducted for the purposes of assessing the results of an activity, product, or service
   */
  OutcomeMeasure: v3_ActReason_OutcomeMeasure,
  /**
   * The substance was administered outside of the recommended schedule or practice.
   */
  AdministeredOutsideRecommendedScheduleOrPractice: v3_ActReason_AdministeredOutsideRecommendedScheduleOrPractice,
  /**
   * To perform one or more operations on information to which the patient has not consented by authorized entities for treating a condition which poses an immediate threat to the patient's health and which requires immediate medical intervention.
   *
   *
   *                            Usage Notes: The patient is unable to provide consent, but the provider determines they have an urgent healthcare related reason to access the record.
   */
  EmergencyTreatmentOverride: v3_ActReason_EmergencyTreatmentOverride,
  /**
   * To perform one or more operations on information to which the patient has not consented because deemed incompetent to provide consent.
   *
   *
   *                            Usage Note: Maps to v2 CON-16 Subject Competence Indicator (ID) 01791 Definition: Identifies whether the subject was deemed competent to provide consent. Refer to table HL7 Table 0136 - Yes/No Indicator and CON-23 Non-Subject Consenter Reason User-defined Table 0502 - Non-Subject Consenter Reason code NC "Subject is not competent to consent".
   */
  IncompetencyOverride: v3_ActReason_IncompetencyOverride,
  /**
   * To perform one or more operations on information to which the patient declined to consent for providing health care.
   *
   *
   *                            Usage Notes: The patient, while able to give consent, has not.  However the provider believes it is in the patient's interest to access the record without patient consent.
   */
  ProfessionalJudgmentOverride: v3_ActReason_ProfessionalJudgmentOverride,
  /**
   * To perform one or more operations on information to which the patient has not consented for public safety reasons.
   *
   *
   *                            Usage Notes: The patient, while able to give consent, has not.  However, the provider believes that access to masked patient information is justified because of concerns related to public safety.
   */
  PublicSafetyOverride: v3_ActReason_PublicSafetyOverride,
  /**
   * To perform one or more operations on information to which the patient has not consented for third party safety.
   *
   *
   *                            Usage Notes: The patient, while able to give consent, has not.  However, the provider believes that access to masked patient information is justified because of concerns related to the health and safety of one or more third parties.
   */
  ThirdPartySafetyOverride: v3_ActReason_ThirdPartySafetyOverride,
  /**
   * The Patient requested the action
   */
  PatientRequest: v3_ActReason_PatientRequest,
  /**
   * To perform one or more operations on information used for operational activities conducted to administer the delivery of health care to a patient.
   */
  PatientAdministration: v3_ActReason_PatientAdministration,
  /**
   * Definition:To obtain records as part of patient care.
   */
  PatientCare: v3_ActReason_PatientCare,
  /**
   * Definition:Patient changed their mind regarding obtaining medication
   */
  PatientChangedMind: v3_ActReason_PatientChangedMind,
  /**
   * Definition:Patient not eligible for drug
   */
  PatientNotEligible: v3_ActReason_PatientNotEligible,
  /**
   * Definition:The patient or their guardian objects to receiving the vaccine.
   */
  PatientObjection: v3_ActReason_PatientObjection,
  /**
   * Definition:Patient requests information from their profile.
   */
  PatientRequestQuery: v3_ActReason_PatientRequestQuery,
  /**
   * To perform one or more operations on information in response to a patient's request.
   */
  PatientRequested: v3_ActReason_PatientRequested,
  /**
   * To perform one or more operations on information in processes related to ensuring the safety of health care.
   */
  PatientSafety: v3_ActReason_PatientSafety,
  /**
   * Description:Administrative, financial, and contractual processes related to payment for an activity, product, or service
   */
  Payment: v3_ActReason_Payment,
  /**
   * To perform one or more operations on information used for monitoring performance of recommended health care practices and interventions.
   */
  PerformanceMeasure: v3_ActReason_PerformanceMeasure,
  /**
   * Definition:The bulk supply is being transferred to another dispensing facility to.
   *
   *
   *                            Example:Alleviate a temporary shortage.
   */
  PharmacyTransfer: v3_ActReason_PharmacyTransfer,
  /**
   * Definition:The patient or their guardian objects to receiving the vaccine because of philosophical beliefs.
   */
  PhilosophicalObjection: v3_ActReason_PhilosophicalObjection,
  /**
   * The Physician requested the action
   */
  PhysicianRequest: v3_ActReason_PhysicianRequest,
  /**
   * A person becomes a claimant under a property and casualty insurance policy because of a related health condition or injury resulting from a circumstance covered under the terms of the policy.
   *
   *
   *                            Example:  A person is a claimant under a homeowners insurance policy because of an injury sustained on the policyholderaTMs premises.
   */
  PropertyAndCasualtyCondition: v3_ActReason_PropertyAndCasualtyCondition,
  /**
   * To perform one or more operations on information, including genealogical pedigrees, historical records, surveys, family health data, health records, and genetic information, for conducting scientific investigations to obtain health care knowledge. Use of the data must be related to population origins and/or ancestry healthcare research.  For example, gathering genetic specimens from a specific population in order to determine the ancestry and population origins of that group. May be used in combination with clinical trial and other healthcare research purposes of use.
   */
  PopulationOriginsOrAncestryHealthcareResearch: v3_ActReason_PopulationOriginsOrAncestryHealthcareResearch,
  /**
   * To perform one or more operations on information for provision of health care to a population of living subjects, e.g., needle exchange program.
   */
  PopulationHealth: v3_ActReason_PopulationHealth,
  /**
   * Definition:The observation or test occurred due to it being defined in the research protocol, and during an activity or event that was scheduled in the protocol.
   */
  PerProtocol: v3_ActReason_PerProtocol,
  /**
   * Definition:To evaluate the provider's current practice for professional-improvement reasons.
   */
  PracticeReview: v3_ActReason_PracticeReview,
  /**
   * To perform one or more operations on information in preparation for conducting scientific investigation to obtain health care knowledge, such as research on animals or review of patient health records, to determine the feasibility of a clinical trial study; assist with protocol design; or in preparation for institutional review board or ethics committee approval process.  May be post-coordinated or used with other purposes of use such as disease, discipline, specialty, population origins or ancestry, translational healthcare research.
   */
  PreclinicalTrialResearch: v3_ActReason_PreclinicalTrialResearch,
  /**
   * Description:The patient refused to take the product.
   */
  PatientRefuse: v3_ActReason_PatientRefuse,
  /**
   * Description:The patient is pregnant or breast feeding.  The therapy will be resumed when the pregnancy is complete and the patient is no longer breastfeeding.
   */
  ParentIsPregnantBreastFeeding: v3_ActReason_ParentIsPregnantBreastFeeding,
  /**
   * Description:Operational activities conducted to meet program accounting requirements related to an activity, product, or service
   */
  ProgramReporting: v3_ActReason_ProgramReporting,
  /**
   * Description:This product is not available or manufactured.
   */
  ProductNotAvailable: v3_ActReason_ProductNotAvailable,
  PROA: v3_ActReason_PROA,
  /**
   * Description: The substance was recalled by the manufacturer.
   */
  ProductRecall: v3_ActReason_ProductRecall,
  /**
   * Definition:The bulk supply is intended for dispensing according to a specific program.
   *
   *
   *                            Example:Mass immunization.
   */
  ProgramUse: v3_ActReason_ProgramUse,
  /**
   * Definition:Patient does not meet required protocol
   */
  ProtocolNotMet: v3_ActReason_ProtocolNotMet,
  /**
   * Definition:Provider is not authorized to prescribe or dispense
   */
  ProviderNotAuthorized: v3_ActReason_ProviderNotAuthorized,
  /**
   * To perform one or more operations on information for conducting public health activities, such as the reporting of notifiable conditions.
   */
  PublicHealth: v3_ActReason_PublicHealth,
  /**
   * Reason for performing one or more operations on information, which may be permitted by source system's security policy in accordance with one or more privacy policies and consent directives.
   *
   *
   *                            Usage Notes: The rationale or purpose for an act relating to the management of personal health information, such as collecting personal health information for research or public health purposes.
   */
  PurposeOfUse: v3_ActReason_PurposeOfUse,
  /**
   * To perform one or more operations on information in response to a request by a person appointed as the patient's legal representative.
   */
  PowerOfAttorney: v3_ActReason_PowerOfAttorney,
  /**
   * Description:Operational activities conducted for the purposes of improving the quality of an activity, product, or service
   */
  QualityImprovement: v3_ActReason_QualityImprovement,
  /**
   * Definition:Patient requested a revised quantity of medication
   */
  ChangeSupplyQuantity: v3_ActReason_ChangeSupplyQuantity,
  /**
   * Description:The manufacturer or other agency has requested that stocks of a medication be removed from circulation.
   */
  ProductRecalled: v3_ActReason_ProductRecalled,
  /**
   * To perform one or more operations on information used within the health records management process.
   */
  RecordsManagement: v3_ActReason_RecordsManagement,
  RECOV: v3_ActReason_RECOV,
  /**
   * Client was registered in error.
   */
  RegisteredInError: v3_ActReason_RegisteredInError,
  /**
   * Description:Review for the purpose of regulatory compliance.
   */
  RegulatoryReview: v3_ActReason_RegulatoryReview,
  /**
   * Definition:The patient or their guardian objects to receiving the vaccine on religious grounds.
   */
  ReligiousObjection: v3_ActReason_ReligiousObjection,
  /**
   * To perform one or more operations on information about the amount remitted for a health care claim.
   */
  RemittanceAdvice: v3_ActReason_RemittanceAdvice,
  /**
   * Description:Item in current order is no longer in use as requested and a new one has/will be created to replace it.
   */
  ChangeInOrder: v3_ActReason_ChangeInOrder,
  /**
   * Description:Current order was issued with incorrect data and a new order has/will be created to replace it.
   */
  ErrorInOrder: v3_ActReason_ErrorInOrder,
  /**
   * Description:Investigative activities conducted for the purposes of obtaining knowledge
   */
  Research: v3_ActReason_Research,
  /**
   * Definition:The event occurred so that a test or observation performed at a prior event could be performed again due to conditions set forth in the protocol.
   */
  Retest: v3_ActReason_Retest,
  RETRO: v3_ActReason_RETRO,
  /**
   * Moved at the request of the patient.
   */
  Request: v3_ActReason_Request,
  /**
   * Indicates that the decision to substitute or to not substitute was driven by a jurisdictional regulatory requirement mandating or prohibiting substitution.
   */
  RegulatoryRequirement: v3_ActReason_RegulatoryRequirement,
  /**
   * Definition:To provide research data, as authorized by the patient.
   */
  Research_2: v3_ActReason_Research_2,
  /**
   * Description:The patient is believed to be allergic to a substance that is part of the therapy and the therapy is being temporarily withdrawn to confirm.
   */
  Allergy: v3_ActReason_Allergy,
  /**
   * Definition:The event occurred due to it being scheduled in the research protocol.
   */
  Scheduled: v3_ActReason_Scheduled,
  /**
   * Description:The drug interacts with a short-term treatment that is more urgently required.  This order will be resumed when the short-term treatment is complete.
   */
  DrugInteractsWithAnotherDrug: v3_ActReason_DrugInteractsWithAnotherDrug,
  /**
   * Description:Another short-term co-occurring therapy fulfills the same purpose as this therapy.  This therapy will be resumed when the co-occuring therapy is complete.
   */
  DuplicateTherapy: v3_ActReason_DuplicateTherapy,
  /**
   * Description:The patient is believed to have an intolerance to a substance that is part of the therapy and the therapy is being temporarily withdrawn to confirm.
   */
  SuspectedIntolerance: v3_ActReason_SuspectedIntolerance,
  /**
   * Description:The level of coverage under the policy or program is available to a subscriber's spouse and children
   */
  SpouseAndChildren: v3_ActReason_SpouseAndChildren,
  /**
   * Description:The level of coverage under the policy or program is available only to a subscribers spouse
   */
  SpouseOnly: v3_ActReason_SpouseOnly,
  /**
   * Description:Provision of a service, product, or capability to an individual or organization
   */
  Service: v3_ActReason_Service,
  /**
   * A person becomes eligible for a program based on statutory criteria.
   *
   *
   *                            Examples:  A person is a member of an indigenous group, a veteran of military service, or  in the U.S., a recipient of adoption assistance and foster care under Title IV-E of the Social Security.
   */
  StatutoryEligibility: v3_ActReason_StatutoryEligibility,
  /**
   * Renewing or original prescriber informed patient to stop using the medication.
   */
  PrescriberStoppedMedicationForPatient: v3_ActReason_PrescriberStoppedMedicationForPatient,
  /**
   * To perform one or more operations on information in response to a request by a person authorized by the patient.
   */
  SupportNetwork: v3_ActReason_SupportNetwork,
  /**
   * Description:The drug is contraindicated for patients receiving surgery and the patient is scheduled to be admitted for surgery in the near future.  The drug will be resumed when the patient has sufficiently recovered from the surgery.
   */
  PatientScheduledForSurgery: v3_ActReason_PatientScheduledForSurgery,
  /**
   * Description:Operational activities conducted to administer the electronic systems used for an activity, product, or service
   */
  SystemAdministration: v3_ActReason_SystemAdministration,
  /**
   * To perform one or more operations on information to design, develop, implement, test, or deploy a healthcare system or application.
   */
  SystemDevelopment: v3_ActReason_SystemDevelopment,
  /**
   * The prescribed product has specific clinical release or other therapeutic characteristics not shared by other substitutable medications.
   */
  TherapeuticCharacteristics: v3_ActReason_TherapeuticCharacteristics,
  /**
   * To perform one or more operations on information used to prevent injury or disease to living subjects who may be the target of violence.
   */
  Threat: v3_ActReason_Threat,
  /**
   * Definition:The patient should have medication remaining.
   */
  TooEarly: v3_ActReason_TooEarly,
  /**
   * To perform one or more operations on information used in training and education.
   */
  Training: v3_ActReason_Training,
  TRAN: v3_ActReason_TRAN,
  /**
   * To perform one or more operations on information for conducting scientific investigations to obtain health care knowledge related to evidence based medicine during the course of providing healthcare treatment.  Sometimes referred to as "bench to bedside", which is the iterative feedback loop between healthcare research and clinical trials with input from information collected in the course of routine provision of healthcare. For example, by extending a patient encounter to conduct a survey related to a research topic such as attitudes about use of a wellness device that a patient agreed to use. May be used in combination with clinical trial and other healthcare research purposes of use.
   */
  TranslationalHealthcareResearch: v3_ActReason_TranslationalHealthcareResearch,
  /**
   * To perform one or more operations on information for provision of health care.
   */
  Treatment: v3_ActReason_Treatment,
  /**
   * Definition: The specific manufactured drug is part of a clinical trial.
   */
  ClinicalTrialDrug: v3_ActReason_ClinicalTrialDrug,
  /**
   * Definition:The event occurred in order to terminate the subject's participation in the study.
   */
  Termination: v3_ActReason_Termination,
  /**
   * Information is separated from the record.
   */
  UnmergeData: v3_ActReason_UnmergeData,
  /**
   * Description:<The patient is not (or is no longer) able to use the medication in a manner prescribed.
   *
   *
   *                            Example:CanaTMt swallow.
   */
  UnableToUse: v3_ActReason_UnableToUse,
  /**
   * Definition:The target facility does not recognize the dispensing facility.
   */
  UnknownTarget: v3_ActReason_UnknownTarget,
  /**
   * Definition:The event that occurred was initiated by a study participant (e.g. the subject or the investigator), and did not occur for protocol reasons.
   */
  Unscheduled: v3_ActReason_Unscheduled,
  /**
   * :The observation or test occurred as defined in the research protocol, but at a point in time not specified in the study protocol.
   */
  PerDefinition: v3_ActReason_PerDefinition,
  /**
   * Definition:The intended vaccine has expired or is otherwise believed to no longer be effective.
   *
   *
   *                            Example:Due to temperature exposure.
   */
  VaccineEfficacyConcerns: v3_ActReason_VaccineEfficacyConcerns,
  /**
   * Definition:The patient or their guardian objects to receiving the vaccine because of concerns over its safety.
   */
  VaccineSafetyConcerns: v3_ActReason_VaccineSafetyConcerns,
  /**
   * Description:To validate the patient's record.
   *
   *
   *                            Example:Merging or unmerging records.
   */
  ValidationReview: v3_ActReason_ValidationReview,
  /**
   * A person becomes a claimant under a motor vehicle accident insurance because of a motor vehicle accident related health condition or injury.
   */
  MotorVehicleAccidentVictim: v3_ActReason_MotorVehicleAccidentVictim,
  /**
   * Description:The patient was previously receiving a medication contraindicated with the current medication.  The current medication will remain on hold until the prior medication has been cleansed from their system.
   */
  WaitingForOldDrugToWashOut: v3_ActReason_WaitingForOldDrugToWashOut,
  /**
   * A person becomes eligible for insurance or a program because of a work related health condition or injury.
   *
   *
   *                            Example:  A person is a claimant under the U.S. Black Lung Program.
   */
  WorkRelated: v3_ActReason_WorkRelated,
  /**
   * Description: The record and version requested to update is not the current version.
   */
  WrongVersion: v3_ActReason_WrongVersion,
};
const v3_ActRelationshipCheckpoint_Beginning: Coding = {
  code: 'B',
  display: 'beginning',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipCheckpoint',
};
const v3_ActRelationshipCheckpoint_End: Coding = {
  code: 'E',
  display: 'end',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipCheckpoint',
};
const v3_ActRelationshipCheckpoint_Entry: Coding = {
  code: 'S',
  display: 'entry',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipCheckpoint',
};
const v3_ActRelationshipCheckpoint_Through: Coding = {
  code: 'T',
  display: 'through',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipCheckpoint',
};
const v3_ActRelationshipCheckpoint_Exit: Coding = {
  code: 'X',
  display: 'exit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipCheckpoint',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3ActRelationshipCheckpoint = {
  /**
   * Condition is tested every time before execution of the service (WHILE condition DO service).
   */
  Beginning: v3_ActRelationshipCheckpoint_Beginning,
  /**
   * Condition is tested at the end of a repeated service execution.  The service is repeated only if the condition is true (DO service WHILE condition).
   */
  End: v3_ActRelationshipCheckpoint_End,
  /**
   * Condition is tested once before the service is executed (IF condition THEN service).
   */
  Entry: v3_ActRelationshipCheckpoint_Entry,
  /**
   * Condition must be true throughout the execution and the service is interrupted (asynchronously) as soon as the condition turns false (asynchronous WHILE loop).  The service must be interruptible.
   */
  Through: v3_ActRelationshipCheckpoint_Through,
  /**
   * Condition is a loop checkpoint, i.e. it is a step of an activity plan and, if negative causes the containing loop to exit.
   */
  Exit: v3_ActRelationshipCheckpoint_Exit,
};
const v3_ActRelationshipType_ActiveImmunizationAgainst: Coding = {
  code: 'ACTIMM',
  display: 'active immunization against',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_AdjunctiveTreatment: Coding = {
  code: 'ADJUNCT',
  display: 'adjunctive treatment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Blocks: Coding = {
  code: 'BLOCK',
  display: 'blocks',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasContraIndication: Coding = {
  code: 'CIND',
  display: 'has contra-indication',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Diagnoses: Coding = {
  code: 'DIAG',
  display: 'diagnoses',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ImmunizationAgainst: Coding = {
  code: 'IMM',
  display: 'immunization against',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Mitigates: Coding = {
  code: 'MITGT',
  display: 'mitigates',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_MaintenanceTreatment: Coding = {
  code: 'MTREAT',
  display: 'maintenance treatment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Palliates: Coding = {
  code: 'PALLTREAT',
  display: 'palliates',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_PassiveImmunizationAgainst: Coding = {
  code: 'PASSIMM',
  display: 'passive immunization against',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasPreCondition: Coding = {
  code: 'PRCN',
  display: 'has pre-condition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ProphylaxisOf: Coding = {
  code: 'PRYLX',
  display: 'prophylaxis of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Recovers: Coding = {
  code: 'RCVY',
  display: 'recovers',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasReason: Coding = {
  code: 'RSON',
  display: 'has reason',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_SymptomaticRelief: Coding = {
  code: 'SYMP',
  display: 'symptomatic relief',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Treats: Coding = {
  code: 'TREAT',
  display: 'treats',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasTrigger: Coding = {
  code: 'TRIG',
  display: 'has trigger',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
/**
 *  Specifies under what circumstances (target Act) the source-Act may, must, must not or has occurred
 */
export const V3ActRelationshipConditional = {
  /**
   * Description: The source act is intended to provide active immunity against the effects of the target act (the target act describes an infectious disease)
   */
  ActiveImmunizationAgainst: v3_ActRelationshipType_ActiveImmunizationAgainst,
  /**
   * Description: The source act is intended to offer an additional treatment for the management or cure of a pre-existing adverse situation described by the target act. This is not limited to diseases but can apply to any adverse situation or condition of medical or technical nature.  It is not a requirement that the non-adjunctive treatment is explicitly specified.
   */
  AdjunctiveTreatment: v3_ActRelationshipType_AdjunctiveTreatment,
  /**
   * Definition: The source act is performed to block the effects of the target act.  This act relationship should be used when describing near miss type incidents where potential harm could have occurred, but the action described in the source act blocked the potential harmful effects of the incident actually occurring.
   */
  Blocks: v3_ActRelationshipType_Blocks,
  /**
   * A contraindication is just a negation of a reason, i.e. it gives a condition under which the action is not to be done. Both, source and target can be any kind of service; target service is in criterion mood. How the strength of a contraindication is expressed (e.g., relative, absolute) is left as an open issue. The priorityNumber attribute could be used.
   */
  HasContraIndication: v3_ActRelationshipType_HasContraIndication,
  /**
   * Description: The source act is intended to help establish the presence of a (an adverse) situation described by the target act. This is not limited to diseases but can apply to any adverse situation or condition of medical or technical nature.
   */
  Diagnoses: v3_ActRelationshipType_Diagnoses,
  /**
   * Description: The source act is intented to provide immunity against the effects of the target act (the target act describes an infectious disease)
   */
  ImmunizationAgainst: v3_ActRelationshipType_ImmunizationAgainst,
  /**
   * The source act removes or lessens the occurrence or effect of the target act.
   */
  Mitigates: v3_ActRelationshipType_Mitigates,
  /**
   * Description: The source act is intended to provide long term maintenance improvement or management of a pre-existing adverse situation described by the target act. This is not limited to diseases but can apply to any adverse situation or condition of medical or technical nature.
   */
  MaintenanceTreatment: v3_ActRelationshipType_MaintenanceTreatment,
  /**
   * Description: The source act is intended to provide palliation for the effects of the target act.
   */
  Palliates: v3_ActRelationshipType_Palliates,
  /**
   * Description: The source act is intended to provide passive immunity against the effects of the target act (the target act describes an infectious disease).
   */
  PassiveImmunizationAgainst: v3_ActRelationshipType_PassiveImmunizationAgainst,
  /**
   * A requirement to be true before a service is performed. The target can be any service in criterion mood.  For multiple pre-conditions a conjunction attribute (AND, OR, XOR) is applicable.
   */
  HasPreCondition: v3_ActRelationshipType_HasPreCondition,
  /**
   * Description: The source act is intended to reduce the risk of of an adverse situation to emerge as described by the target act. This is not limited to diseases but can apply to any adverse situation or condition of medical or technical nature.
   */
  ProphylaxisOf: v3_ActRelationshipType_ProphylaxisOf,
  /**
   * Definition: The source act is performed to recover from the effects of the target act.
   */
  Recovers: v3_ActRelationshipType_Recovers,
  /**
   * Description: The reason or rationale for a service. A reason link is weaker than a trigger, it only suggests that some service may be or might have been a reason for some action, but not that this reason requires/required the action to be taken. Also, as opposed to the trigger, there is no strong timely relation between the reason and the action.  As well as providing various types of information about the rationale for a service, the RSON act relationship is routinely used between a SBADM act and an OBS act to describe the indication for use of a medication.  Child concepts may be used to describe types of indication.
   *
   *
   *                            Discussion: In prior releases, the code "SUGG" (suggests) was expressed as "an inversion of the reason link." That code has been retired in favor of the inversion indicator that is an attribute of ActRelationship.
   */
  HasReason: v3_ActRelationshipType_HasReason,
  /**
   * Description: The source act is intented to provide symptomatic relief for the effects of the target act.
   */
  SymptomaticRelief: v3_ActRelationshipType_SymptomaticRelief,
  /**
   * Description: The source act is intended to improve a pre-existing adverse situation described by the target act. This is not limited to diseases but can apply to any adverse situation or condition of medical or technical nature.
   */
  Treats: v3_ActRelationshipType_Treats,
  /**
   * A pre-condition that if true should result in the source Act being executed.  The target is in typically in criterion mood.  When reported after the fact (i.e. the criterion has been met) it may be in Event mood.  A delay between the trigger and the triggered action can be specified.
   *
   *
   *                            Discussion: This includes the concept of a  required act for a service or financial instrument such as an insurance plan or policy. In such cases, the trigger is the occurrence of a specific condition such as coverage limits being exceeded.
   */
  HasTrigger: v3_ActRelationshipType_HasTrigger,
};
const v3_ActRelationshipType_Fulfills: Coding = {
  code: 'FLFS',
  display: 'fulfills',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Occurrence: Coding = {
  code: 'OCCR',
  display: 'occurrence',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ReferencesOrder: Coding = {
  code: 'OREF',
  display: 'references order',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_SchedulesRequest: Coding = {
  code: 'SCH',
  display: 'schedules request',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
/**
 *  The source act fulfills (in whole or in part) the target act. Source act must be in a mood equal or more actual than the target act.
 */
export const V3ActRelationshipFulfills = {
  /**
   * The source act fulfills (in whole or in part) the target act. Source act must be in a mood equal or more actual than the target act.
   */
  Fulfills: v3_ActRelationshipType_Fulfills,
  /**
   * The source act is a single occurrence of a repeatable target act. The source and target act can be in any mood on the "completion track" but the source act must be as far as or further along the track than the target act (i.e., the occurrence of an intent can be an event but not vice versa).
   */
  Occurrence: v3_ActRelationshipType_Occurrence,
  /**
   * Relates either an appointment request or an appointment to the order for the service being scheduled.
   */
  ReferencesOrder: v3_ActRelationshipType_ReferencesOrder,
  /**
   * Associates a specific time (and associated resources) with a scheduling request or other intent.
   */
  SchedulesRequest: v3_ActRelationshipType_SchedulesRequest,
};
const v3_ActRelationshipType_Arrival: Coding = {
  code: 'ARR',
  display: 'arrival',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasComponent: Coding = {
  code: 'COMP',
  display: 'has component',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasControlVariable: Coding = {
  code: 'CTRLV',
  display: 'has control variable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Departure: Coding = {
  code: 'DEP',
  display: 'departure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasMember: Coding = {
  code: 'MBR',
  display: 'has member',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasPart: Coding = {
  code: 'PART',
  display: 'has part',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasStep: Coding = {
  code: 'STEP',
  display: 'has step',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
/**
 *  A collection of sub-services as steps or subtasks performed for the source service. Services may be performed sequentially or concurrently.
 */
export const V3ActRelationshipHasComponent = {
  /**
   * The relationship that links to a Transportation Act (target) from another Act (source) indicating that the subject of the source Act entered into the source Act by means of the target Transportation act.
   */
  Arrival: v3_ActRelationshipType_Arrival,
  /**
   * The target act is a component of the source act, with no semantics regarding composition or aggregation implied.
   */
  HasComponent: v3_ActRelationshipType_HasComponent,
  /**
   * A relationship from an Act to a Control Variable.  For example, if a Device makes an Observation, this relates the Observation to its Control Variables documenting  the device's settings that influenced the observation.
   */
  HasControlVariable: v3_ActRelationshipType_HasControlVariable,
  /**
   * The relationship that links to a Transportation Act (target) from another Act (source) indicating that the subject of the source Act departed from the source Act by means of the target Transportation act.
   */
  Departure: v3_ActRelationshipType_Departure,
  /**
   * The target Acts are aggregated by the source Act.  Target Acts may have independent existence, participate in multiple ActRelationships, and do not contribute to the meaning of the source.
   *
   *
   *                            UsageNotes: This explicitly represents the conventional notion of aggregation.  The target Act is part of a collection of Acts (no implication is made of cardinality, a source of Acts may contain zero, one, or more member target Acts).
   *
   *                         It is expected that this will be primarily used with _ActClassRecordOrganizer, BATTERY, and LIST
   */
  HasMember: v3_ActRelationshipType_HasMember,
  /**
   * The source Act is a composite of the target Acts. The target Acts do not have an existence independent of the source Act.
   *
   *
   *                            UsageNote: In UML 1.1, this is a "composition" defined as:
   *                            "A form of aggregation with strong ownership and coincident lifetime as part of the whole. Parts with non-fixed multiplicity may be created after the composite itself, but once created they live and die with it (i.e., they share lifetimes). Such parts can also be explicitly removed before the death of the composite. Composition may be recursive."
   */
  HasPart: v3_ActRelationshipType_HasPart,
  /**
   * A collection of sub-services as steps or subtasks performed for the source service. Services may be performed sequentially or concurrently.
   *
   *
   *                            UsageNotes: Sequence of steps may be indicated by use of _ActRelationshipTemporallyPertains, as well as via  ActRelationship.sequenceNumber, ActRelationship.pauseQuantity, Target.priorityCode.
   *
   *
   *                            OpenIssue: Need Additional guidelines on when each approach should be used.
   */
  HasStep: v3_ActRelationshipType_HasStep,
};
const v3_ActRelationshipJoin_Detached: Coding = {
  code: 'D',
  display: 'detached',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipJoin',
};
const v3_ActRelationshipJoin_Kill: Coding = {
  code: 'K',
  display: 'kill',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipJoin',
};
const v3_ActRelationshipJoin_Wait: Coding = {
  code: 'W',
  display: 'wait',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipJoin',
};
const v3_ActRelationshipJoin_ExclusiveWait: Coding = {
  code: 'X',
  display: 'exclusive wait',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipJoin',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3ActRelationshipJoin = {
  /**
   * Detach this branch from the other branches so it will not be resynchronized with the other branches.
   */
  Detached: v3_ActRelationshipJoin_Detached,
  /**
   * When all other concurrent branches are terminated, interrupt and discontinue this branch.
   */
  Kill: v3_ActRelationshipJoin_Kill,
  /**
   * Wait for this branch to terminate.
   */
  Wait: v3_ActRelationshipJoin_Wait,
  /**
   * Wait for any one of the branches in the set of exclusive wait branches to terminate, then discontinue all the other exclusive wait branches.
   */
  ExclusiveWait: v3_ActRelationshipJoin_ExclusiveWait,
};
const v3_ActRelationshipType_HasPertinentInformation: Coding = {
  code: 'PERT',
  display: 'has pertinent information',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
/**
 *  This is a very unspecific relationship from one item of clinical information to another.  It does not judge about the role the pertinent information plays.
 */
export const V3ActRelationshipPertains = {
  /**
   * This is a very unspecific relationship from one item of clinical information to another.  It does not judge about the role the pertinent information plays.
   */
  HasPertinentInformation: v3_ActRelationshipType_HasPertinentInformation,
};
const v3_ActRelationshipSplit_ExclusiveTryOnce: Coding = {
  code: 'E1',
  display: 'exclusive try once',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSplit',
};
const v3_ActRelationshipSplit_ExclusiveWait: Coding = {
  code: 'EW',
  display: 'exclusive wait',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSplit',
};
const v3_ActRelationshipSplit_InclusiveTryOnce: Coding = {
  code: 'I1',
  display: 'inclusive try once',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSplit',
};
const v3_ActRelationshipSplit_InclusiveWait: Coding = {
  code: 'IW',
  display: 'inclusive wait',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSplit',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3ActRelationshipSplit = {
  /**
   * The pre-condition associated with the branch is evaluated once and if true the branch may be entered. All other exclusive branches compete with each other and only one will be selected. This implements a COND, IF and CASE conditionals, or "XOR-split." The order in which the branches are considered may be specified in the priorityNumber attribute.
   */
  ExclusiveTryOnce: v3_ActRelationshipSplit_ExclusiveTryOnce,
  /**
   * A branch is selected as soon as the pre-condition associated with the branch evaluates to true.  If the condition is false, the branch may be entered later, when the condition turns true.  All other exclusive branches compete with each other and only one will be selected.  Each waiting branch executes in parallel with the default join code wait  (see below). The order in which the branches are considered may be specified in the Service_relationship.priority_nmb.
   */
  ExclusiveWait: v3_ActRelationshipSplit_ExclusiveWait,
  /**
   * A branch is executed if its associated preconditions permit. If associated preconditions do not permit, the branch is dropped.  Inclusive branches are not suppressed and do not suppress other branches.
   */
  InclusiveTryOnce: v3_ActRelationshipSplit_InclusiveTryOnce,
  /**
   * A branch is executed as soon as its associated conditions permit.  If the condition is false, the branch may be entered later, when the condition turns true.  Inclusive branches are not suppressed and do not suppress other branches.  Each waiting branch executes in parallel with the default join code wait  (see below).
   */
  InclusiveWait: v3_ActRelationshipSplit_InclusiveWait,
};
const v3_ActRelationshipSubset_ParticipationSubset: Coding = {
  code: '_ParticipationSubset',
  display: 'ParticipationSubset',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
const v3_ActRelationshipSubset_ActRelationshipExpectedSubset: Coding = {
  code: 'ActRelationshipExpectedSubset',
  display: 'ActRelationshipExpectedSubset',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
const v3_ActRelationshipSubset_ActRelationshipPastSubset: Coding = {
  code: 'ActRelationshipPastSubset',
  display: 'ActRelationshipPastSubset',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
const v3_ActRelationshipSubset_FirstKnown: Coding = {
  code: 'FIRST',
  display: 'first known',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
const v3_ActRelationshipSubset_FutureSummary: Coding = {
  code: 'FUTSUM',
  display: 'future summary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
const v3_ActRelationshipSubset_ExpectedFuture: Coding = {
  code: 'FUTURE',
  display: 'expected future',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
const v3_ActRelationshipSubset_ExpectedLast: Coding = {
  code: 'LAST',
  display: 'expected last',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
const v3_ActRelationshipSubset_Maximum: Coding = {
  code: 'MAX',
  display: 'maximum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
const v3_ActRelationshipSubset_Minimum: Coding = {
  code: 'MIN',
  display: 'minimum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
const v3_ActRelationshipSubset_ExpectedNext: Coding = {
  code: 'NEXT',
  display: 'expected next',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
const v3_ActRelationshipSubset_Previous: Coding = {
  code: 'PAST',
  display: 'previous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
const v3_ActRelationshipSubset_PreviousSummary: Coding = {
  code: 'PREVSUM',
  display: 'previous summary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
const v3_ActRelationshipSubset_MostRecent: Coding = {
  code: 'RECENT',
  display: 'most recent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
const v3_ActRelationshipSubset_Summary: Coding = {
  code: 'SUM',
  display: 'summary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipSubset',
};
/**
 *  <ns1:p>Used to indicate that the target of the relationship will be a filtered subset of the total related set of targets.</ns1:p><ns1:p>Used when there is a need to limit the number of components to the first, the last, the next, the total, the average or some other filtered or calculated subset.</ns1:p>
 */
export const V3ActRelationshipSubset = {
  /**
   * Used to indicate that the participation is a filtered subset of the total participations of the same type owned by the Act.
   *
   *                         Used when there is a need to limit the participations to the first, the last, the next or some other filtered subset.
   */
  ParticipationSubset: v3_ActRelationshipSubset_ParticipationSubset,
  /**
   * ActRelationshipExpectedSubset
   */
  ActRelationshipExpectedSubset: v3_ActRelationshipSubset_ActRelationshipExpectedSubset,
  /**
   * ActRelationshipPastSubset
   */
  ActRelationshipPastSubset: v3_ActRelationshipSubset_ActRelationshipPastSubset,
  /**
   * Restricted to the earliest known occurrence that occurred or was scheduled to occur in the past. The Act with the lowest effective time. ('now' is the time the instance is authored.)
   */
  FirstKnown: v3_ActRelationshipSubset_FirstKnown,
  /**
   * Represents a 'summary' of all acts that are scheduled to occur in the future (whose effective time is greater than 'now' where is the time the instance is authored.). The effectiveTime represents the outer boundary of all occurrences, repeatNumber represents the total number of repetitions, etc.
   */
  FutureSummary: v3_ActRelationshipSubset_FutureSummary,
  /**
   * An occurrence that is scheduled to occur in the future. An Act whose effective time is greater than 'now', where 'now' is the time the instance is authored.
   */
  ExpectedFuture: v3_ActRelationshipSubset_ExpectedFuture,
  /**
   * Restricted to the latest known occurrence that is scheduled to occur. The Act with the highest known effective time.
   */
  ExpectedLast: v3_ActRelationshipSubset_ExpectedLast,
  /**
   * The occurrence whose value attribute is greater than all other occurrences at the time the instance is created.
   */
  Maximum: v3_ActRelationshipSubset_Maximum,
  /**
   * The occurrence whose value attribute is less than all other occurrences at the time the instance is created.
   */
  Minimum: v3_ActRelationshipSubset_Minimum,
  /**
   * Restricted to the nearest recent known occurrence scheduled to occur in the future. The Act with the lowest effective time, still greater than 'now'. ('now' is the time the instance is authored.)
   */
  ExpectedNext: v3_ActRelationshipSubset_ExpectedNext,
  /**
   * An occurrence that occurred or was scheduled to occur in the past. An Act whose effective time is less than 'now'. ('now' is the time the instance is authored.)
   */
  Previous: v3_ActRelationshipSubset_Previous,
  /**
   * Represents a 'summary' of all acts that previously occurred or were scheduled to occur. The effectiveTime represents the outer boundary of all occurrences, repeatNumber represents the total number of repetitions, etc. ('now' is the time the instance is authored.)
   */
  PreviousSummary: v3_ActRelationshipSubset_PreviousSummary,
  /**
   * Restricted to the most recent known occurrence that occurred or was scheduled to occur in the past. The Act with the most recent effective time, still less than 'now'. ('now' is the time the instance is authored.)
   */
  MostRecent: v3_ActRelationshipSubset_MostRecent,
  /**
   * Represents a 'summary' of all acts that have occurred or were scheduled to occur and which are scheduled to occur in the future. The effectiveTime represents the outer boundary of all occurrences, repeatNumber represents the total number of repetitions, etc.
   */
  Summary: v3_ActRelationshipSubset_Summary,
};
const v3_ActRelationshipType_ActClassTemporallyPertains: Coding = {
  code: '_ActClassTemporallyPertains',
  display: 'ActClassTemporallyPertains',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ActRelationshipAccounting: Coding = {
  code: '_ActRelationshipAccounting',
  display: 'ActRelationshipAccounting',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ActRelationshipConditional: Coding = {
  code: '_ActRelationshipConditional',
  display: 'ActRelationshipConditional',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ActRelationshipCostTracking: Coding = {
  code: '_ActRelationshipCostTracking',
  display: 'ActRelationshipCostTracking',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ActRelationshipPosting: Coding = {
  code: '_ActRelationshipPosting',
  display: 'ActRelationshipPosting',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ActRelationshipTemporallyPertains: Coding = {
  code: '_ActRelationshipTemporallyPertains',
  display: 'ActRelationshipTemporallyPertains',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ActRelationshipTemporallyPertainsApproximates: Coding = {
  code: '_ActRelationshipTemporallyPertainsApproximates',
  display: 'ActRelationshipTemporallyPertainsApproximates',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ActRelationsipObjective: Coding = {
  code: '_ActRelationsipObjective',
  display: 'Act Relationsip Objective',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_IsAppendage: Coding = {
  code: 'APND',
  display: 'is appendage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ActRelationshipType: Coding = {
  code: 'ART',
  display: 'act relationship type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_AuthorizedBy: Coding = {
  code: 'AUTH',
  display: 'authorized by',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasBaseline: Coding = {
  code: 'BSLN',
  display: 'has baseline',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_IsEtiologyFor: Coding = {
  code: 'CAUS',
  display: 'is etiology for',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasCharge: Coding = {
  code: 'CHRG',
  display: 'has charge',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_CompliesWith: Coding = {
  code: 'COMPLY',
  display: 'complies with',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ConcurrentWith: Coding = {
  code: 'CONCURRENT',
  display: 'concurrent with',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasCost: Coding = {
  code: 'COST',
  display: 'has cost',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_CoveredBy: Coding = {
  code: 'COVBY',
  display: 'covered by',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasCredit: Coding = {
  code: 'CREDIT',
  display: 'has credit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_CurativeIndication: Coding = {
  code: 'CURE',
  display: 'curative indication',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_AdjunctCurativeIndication: Coding = {
  code: 'CURE.ADJ',
  display: 'adjunct curative indication',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasDebit: Coding = {
  code: 'DEBIT',
  display: 'has debit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Documents: Coding = {
  code: 'DOC',
  display: 'documents',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_IsDerivedFrom: Coding = {
  code: 'DRIV',
  display: 'is derived from',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_OccursDuring: Coding = {
  code: 'DURING',
  display: 'occurs during',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EndsAfterEndOf: Coding = {
  code: 'EAE',
  display: 'ends after end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EndsAfterOrConcurrentWithEndOf: Coding = {
  code: 'EAEORECW',
  display: 'ends after or concurrent with end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EndsAfterStartOf: Coding = {
  code: 'EAS',
  display: 'ends after start of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EndsAfterOrConcurrentWithStartOf: Coding = {
  code: 'EASORECWS',
  display: 'ends after or concurrent with start of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EndsBeforeEndOf: Coding = {
  code: 'EBE',
  display: 'ends before end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EndsBeforeOrConcurrentWithEndOf: Coding = {
  code: 'EBEORECW',
  display: 'ends before or concurrent with end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EndsBeforeStartOf: Coding = {
  code: 'EBS',
  display: 'ends before start of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EndsBeforeOrConcurrentWithStartOf: Coding = {
  code: 'EBSORECWS',
  display: 'ends before or concurrent with start of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EndsConcurrentWith: Coding = {
  code: 'ECW',
  display: 'ends concurrent with',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EndsConcurrentWithStartOf: Coding = {
  code: 'ECWS',
  display: 'ends concurrent with start of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EndsDuring: Coding = {
  code: 'EDU',
  display: 'ends during',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EpisodeLink: Coding = {
  code: 'ELNK',
  display: 'episodeLink',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EndsNearEnd: Coding = {
  code: 'ENE',
  display: 'ends near end',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EndsNearStart: Coding = {
  code: 'ENS',
  display: 'ends near start',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ProvidesEvidenceFor: Coding = {
  code: 'EVID',
  display: 'provides evidence for',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ExacerbatedBy: Coding = {
  code: 'EXACBY',
  display: 'exacerbated by',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasExplanation: Coding = {
  code: 'EXPL',
  display: 'has explanation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasGeneralization: Coding = {
  code: 'GEN',
  display: 'has generalization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_EvaluatesGoal: Coding = {
  code: 'GEVL',
  display: 'evaluates (goal)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasGoal: Coding = {
  code: 'GOAL',
  display: 'has goal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_IndependentOfTimeOf: Coding = {
  code: 'INDEPENDENT',
  display: 'independent of time of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_InstantiatesMaster: Coding = {
  code: 'INST',
  display: 'instantiates (master)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_InterferedBy: Coding = {
  code: 'INTF',
  display: 'interfered by',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ItemsLocated: Coding = {
  code: 'ITEMSLOC',
  display: 'items located',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_LimitedBy: Coding = {
  code: 'LIMIT',
  display: 'limited by',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasMetadata: Coding = {
  code: 'META',
  display: 'has metadata',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_IsManifestationOf: Coding = {
  code: 'MFST',
  display: 'is manifestation of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Modifies: Coding = {
  code: 'MOD',
  display: 'modifies',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_MatchesTrigger: Coding = {
  code: 'MTCH',
  display: 'matches (trigger)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_AdjunctMitigation: Coding = {
  code: 'MTGT.ADJ',
  display: 'adjunct mitigation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_AssignsName: Coding = {
  code: 'NAME',
  display: 'assigns name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasContinuingObjective: Coding = {
  code: 'OBJC',
  display: 'has continuing objective',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasFinalObjective: Coding = {
  code: 'OBJF',
  display: 'has final objective',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasOption: Coding = {
  code: 'OPTN',
  display: 'has option',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasOutcome: Coding = {
  code: 'OUTC',
  display: 'has outcome',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_OverlapsWith: Coding = {
  code: 'OVERLAP',
  display: 'overlaps with',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasPreviousInstance: Coding = {
  code: 'PREV',
  display: 'has previous instance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasQualifier: Coding = {
  code: 'QUALF',
  display: 'has qualifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_RACT: Coding = {
  code: 'RACT',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ReChallenge: Coding = {
  code: 'RCHAL',
  display: 're-challenge',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_RefersTo: Coding = {
  code: 'REFR',
  display: 'refers to',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasReferenceValues: Coding = {
  code: 'REFV',
  display: 'has reference values',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_RelievedBy: Coding = {
  code: 'RELVBY',
  display: 'relieved by',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Reverses: Coding = {
  code: 'REV',
  display: 'reverses',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasRisk: Coding = {
  code: 'RISK',
  display: 'has risk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Replaces: Coding = {
  code: 'RPLC',
  display: 'replaces',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsAfterEndOf: Coding = {
  code: 'SAE',
  display: 'starts after end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsAfterOrConcurrentWithEndOf: Coding = {
  code: 'SAEORSCWE',
  display: 'starts after or concurrent with end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsAfterStartOf: Coding = {
  code: 'SAS',
  display: 'starts after start of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsAfterStartOfEndsAfterEndOf: Coding = {
  code: 'SASEAE',
  display: 'starts after start of, ends after end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsAfterStartOfEndsWith: Coding = {
  code: 'SASECWE',
  display: 'starts after start of, ends with',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsAfterOrConcurrentWithStartOf: Coding = {
  code: 'SASORSCW',
  display: 'starts after or concurrent with start of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartAfterStartOfContainsEndOf: Coding = {
  code: 'SASSBEEAS',
  display: 'start after start of, contains end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsBeforeEndOf: Coding = {
  code: 'SBE',
  display: 'starts before end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ContainsEndOf: Coding = {
  code: 'SBEEAE',
  display: 'contains end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsBeforeOrConcurrentWithEndOf: Coding = {
  code: 'SBEORSCWE',
  display: 'starts before or concurrent with end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsBeforeStartOf: Coding = {
  code: 'SBS',
  display: 'starts before start of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ContainsTimeOf: Coding = {
  code: 'SBSEAE',
  display: 'contains time of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ContainsStartOf: Coding = {
  code: 'SBSEAS',
  display: 'contains start of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ContainsStartOfEndsBeforeEndOf: Coding = {
  code: 'SBSEASEBE',
  display: 'contains start of, ends before end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsBeforeStartOfEndsBeforeEndOf: Coding = {
  code: 'SBSEBE',
  display: 'starts before start of, ends before end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsBeforeStartOfEndsWith: Coding = {
  code: 'SBSECWE',
  display: 'starts before start of, ends with',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsBeforeOrConcurrentWithStartOf: Coding = {
  code: 'SBSORSCW',
  display: 'starts before or concurrent with start of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsConcurrentWith: Coding = {
  code: 'SCW',
  display: 'starts concurrent with',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsConcurrentWithEndOf: Coding = {
  code: 'SCWE',
  display: 'starts concurrent with end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsWithEndsAfterEndOf: Coding = {
  code: 'SCWSEAE',
  display: 'starts with, ends after end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsWithEndsBeforeEndOf: Coding = {
  code: 'SCWSEBE',
  display: 'starts with. ends before end of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsDuring: Coding = {
  code: 'SDU',
  display: 'starts during',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_IsSequel: Coding = {
  code: 'SEQL',
  display: 'is sequel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsNearEnd: Coding = {
  code: 'SNE',
  display: 'starts near end',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_StartsNearStart: Coding = {
  code: 'SNS',
  display: 'starts near start',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasSupport: Coding = {
  code: 'SPRT',
  display: 'has support',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasBoundedSupport: Coding = {
  code: 'SPRTBND',
  display: 'has bounded support',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasSubject: Coding = {
  code: 'SUBJ',
  display: 'has subject',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Succeeds: Coding = {
  code: 'SUCC',
  display: 'succeeds',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_SUGG: Coding = {
  code: 'SUGG',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_SummarizedBy: Coding = {
  code: 'SUMM',
  display: 'summarized by',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_UpdatesCondition: Coding = {
  code: 'UPDT',
  display: 'updates (condition)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Uses: Coding = {
  code: 'USE',
  display: 'uses',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_HasValue: Coding = {
  code: 'VALUE',
  display: 'has value',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_ExcerptVerbatim: Coding = {
  code: 'VRXCRPT',
  display: 'Excerpt verbatim',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Excerpts: Coding = {
  code: 'XCRPT',
  display: 'Excerpts',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
const v3_ActRelationshipType_Transformation: Coding = {
  code: 'XFRM',
  display: 'transformation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActRelationshipType',
};
/**
 *  The source is an excerpt from the target.
 */
export const V3ActRelationshipType = {
  /**
   * ActClassTemporallyPertains
   */
  ActClassTemporallyPertains: v3_ActRelationshipType_ActClassTemporallyPertains,
  /**
   * Codes that describe the relationship between an Act and a financial instrument such as a financial transaction, account or invoice element.
   */
  ActRelationshipAccounting: v3_ActRelationshipType_ActRelationshipAccounting,
  /**
   * Specifies under what circumstances (target Act) the source-Act may, must, must not or has occurred
   */
  ActRelationshipConditional: v3_ActRelationshipType_ActRelationshipConditional,
  /**
   * Expresses values for describing the relationship relationship between an InvoiceElement or InvoiceElementGroup and a billable act.
   */
  ActRelationshipCostTracking: v3_ActRelationshipType_ActRelationshipCostTracking,
  /**
   * Expresses values for describing the relationship between a FinancialTransaction and an Account.
   */
  ActRelationshipPosting: v3_ActRelationshipType_ActRelationshipPosting,
  /**
   * Abstract collector for ActRelationhsip types that relate two acts by their timing.
   */
  ActRelationshipTemporallyPertains: v3_ActRelationshipType_ActRelationshipTemporallyPertains,
  /**
   * Abstract collector for ActRelationship types that relate two acts by their approximate timing.
   */
  ActRelationshipTemporallyPertainsApproximates: v3_ActRelationshipType_ActRelationshipTemporallyPertainsApproximates,
  /**
   * The target act is a desired outcome of the source act. Source is any act (typically an intervention). Target must be an observation in criterion mood.
   */
  ActRelationsipObjective: v3_ActRelationshipType_ActRelationsipObjective,
  /**
   * Description: The source act is intended to provide active immunity against the effects of the target act (the target act describes an infectious disease)
   */
  ActiveImmunizationAgainst: v3_ActRelationshipType_ActiveImmunizationAgainst,
  /**
   * Description: The source act is intended to offer an additional treatment for the management or cure of a pre-existing adverse situation described by the target act. This is not limited to diseases but can apply to any adverse situation or condition of medical or technical nature.  It is not a requirement that the non-adjunctive treatment is explicitly specified.
   */
  AdjunctiveTreatment: v3_ActRelationshipType_AdjunctiveTreatment,
  /**
   * An addendum (source) to an existing service object (target), containing supplemental information.  The addendum is itself an original service object linked to the supplemented service object.  The supplemented service object remains in place and its content and status are unaltered.
   */
  IsAppendage: v3_ActRelationshipType_IsAppendage,
  /**
   * The relationship that links to a Transportation Act (target) from another Act (source) indicating that the subject of the source Act entered into the source Act by means of the target Transportation act.
   */
  Arrival: v3_ActRelationshipType_Arrival,
  /**
   * Description: A directed association between a source Act and a target Act.
   *
   *
   *                            Usage Note: This code should never be transmitted in an instance as the value of ActRelationship.typeCode (attribute)
   */
  ActRelationshipType: v3_ActRelationshipType_ActRelationshipType,
  /**
   * A relationship in which the target act authorizes or certifies the source act.
   */
  AuthorizedBy: v3_ActRelationshipType_AuthorizedBy,
  /**
   * Definition: The source act is performed to block the effects of the target act.  This act relationship should be used when describing near miss type incidents where potential harm could have occurred, but the action described in the source act blocked the potential harmful effects of the incident actually occurring.
   */
  Blocks: v3_ActRelationshipType_Blocks,
  /**
   * Indicates that the target observation(s) provide an initial reference for the source observation or observation group.
   *
   *
   *                            UsageConstraints: Both source and target must be Observations or specializations thereof.
   */
  HasBaseline: v3_ActRelationshipType_HasBaseline,
  /**
   * Description: An assertion that an act was the cause of another act.This is stronger and more specific than the support link. The source (cause) is typically an observation, but may be any act, while the target may be any act.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            a growth of Staphylococcus aureus may be considered the cause of an abscess
   *                            contamination of the infusion bag was deemed to be the cause of the infection that the patient experienced
   *                            lack of staff on the shift was deemed to be a supporting factor (proximal factor) causing the patient safety incident where the patient fell out of bed because the  bed-sides had not been put up which caused the night patient to fall out of bed
   */
  IsEtiologyFor: v3_ActRelationshipType_IsEtiologyFor,
  /**
   * A relationship that provides an ability to associate a financial transaction (target) as a charge to a clinical act (source).  A clinical act may have a charge associated with the execution or delivery of the service.
   *
   *                         The financial transaction will define the charge (bill) for delivery or performance of the service.
   *
   *                         Charges and costs are distinct terms.  A charge defines what is charged or billed to another organization or entity within an organization.  The cost defines what it costs an organization to perform or deliver a service or product.
   */
  HasCharge: v3_ActRelationshipType_HasCharge,
  /**
   * A contraindication is just a negation of a reason, i.e. it gives a condition under which the action is not to be done. Both, source and target can be any kind of service; target service is in criterion mood. How the strength of a contraindication is expressed (e.g., relative, absolute) is left as an open issue. The priorityNumber attribute could be used.
   */
  HasContraIndication: v3_ActRelationshipType_HasContraIndication,
  /**
   * The target act is a component of the source act, with no semantics regarding composition or aggregation implied.
   */
  HasComponent: v3_ActRelationshipType_HasComponent,
  /**
   * Description:The source act complies with, adheres to, conforms to, or is permissible under (in whole or in part) the policy, contract, agreement, law, conformance criteria, certification guidelines or requirement conveyed by the target act.
   *
   *                         Examples for compliance relationships are: audits of adherence with a security policy, certificate of conformance to system certification requirements, or consent directive in compliance with or permissible under a privacy policy.
   */
  CompliesWith: v3_ActRelationshipType_CompliesWith,
  /**
   * A relationship in which the source act's effective time is the same as the target act's effective time.
   *
   *
   *                            UsageNote: This code is reflexive.  Therefore its inverse code is itself.
   */
  ConcurrentWith: v3_ActRelationshipType_ConcurrentWith,
  /**
   * A relationship that provides an ability to associate a financial transaction (target) as a cost to a clinical act (source).  A clinical act may have an inherit cost associated with the execution or delivery of the service.
   *
   *                         The financial transaction will define the cost of delivery or performance of the service.
   *
   *                         Charges and costs are distinct terms.  A charge defines what is charged or billed to another organization or entity within an organization.  The cost defines what it costs an organization to perform or deliver a service or product.
   */
  HasCost: v3_ActRelationshipType_HasCost,
  /**
   * A relationship in which the source act is covered by or is under the authority of a target act.  A financial instrument such as an Invoice Element is covered by one or more specific instances of an Insurance Policy.
   */
  CoveredBy: v3_ActRelationshipType_CoveredBy,
  /**
   * A credit relationship ties a financial transaction (target) to an account (source). A credit, once applied (posted), may have either a positive or negative effect on the account balance, depending on the type of account. An asset account credit will decrease the account balance. A non-asset account credit will decrease the account balance.
   */
  HasCredit: v3_ActRelationshipType_HasCredit,
  /**
   * A relationship from an Act to a Control Variable.  For example, if a Device makes an Observation, this relates the Observation to its Control Variables documenting  the device's settings that influenced the observation.
   */
  HasControlVariable: v3_ActRelationshipType_HasControlVariable,
  /**
   * curative indication
   */
  CurativeIndication: v3_ActRelationshipType_CurativeIndication,
  /**
   * adjunct curative indication
   */
  AdjunctCurativeIndication: v3_ActRelationshipType_AdjunctCurativeIndication,
  /**
   * A debit relationship ties a financial transaction (target) to an account (source).  A debit, once applied (posted), may have either a positive or negative effect on the account balance, depending on the type of account.  An asset account debit will increase the account balance.  A non-asset account debit will decrease the account balance.
   */
  HasDebit: v3_ActRelationshipType_HasDebit,
  /**
   * The relationship that links to a Transportation Act (target) from another Act (source) indicating that the subject of the source Act departed from the source Act by means of the target Transportation act.
   */
  Departure: v3_ActRelationshipType_Departure,
  /**
   * Description: The source act is intended to help establish the presence of a (an adverse) situation described by the target act. This is not limited to diseases but can apply to any adverse situation or condition of medical or technical nature.
   */
  Diagnoses: v3_ActRelationshipType_Diagnoses,
  /**
   * The source act documents the target act.
   */
  Documents: v3_ActRelationshipType_Documents,
  /**
   * Associates a derived Act with its input parameters. E.G., an anion-gap observation can be associated as being derived from given sodium-, (potassium-,), chloride-, and bicarbonate-observations. The narrative content (Act.text) of a source act is wholly machine-derived from the collection of target acts.
   */
  IsDerivedFrom: v3_ActRelationshipType_IsDerivedFrom,
  /**
   * A relationship in which the source act's effective time is wholly within the target act's effective time (including end points, as defined in the act's effective times)
   *
   *
   *                            UsageNote: Inverse code is SBSEAE
   */
  OccursDuring: v3_ActRelationshipType_OccursDuring,
  /**
   * A relationship in which the source act ends after the target act ends.
   *
   *
   *                            UsageNote: Inverse code is EBE
   */
  EndsAfterEndOf: v3_ActRelationshipType_EndsAfterEndOf,
  /**
   * A relationship in which the source act's effective time ends after or concurrent with the end of the target act's effective time.
   *
   *
   *                            Usage Note: Inverse code is EBEORECW
   */
  EndsAfterOrConcurrentWithEndOf: v3_ActRelationshipType_EndsAfterOrConcurrentWithEndOf,
  /**
   * A relationship in which the source act ends after the target act starts.
   *
   *
   *                            UsageNote: Inverse code is SBE
   */
  EndsAfterStartOf: v3_ActRelationshipType_EndsAfterStartOf,
  /**
   * A relationship in which the source act's effective time ends after or concurrent with the start of the target act's effective time.
   *
   *
   *                            Usage Note: Inverse code is EBSORECWS
   */
  EndsAfterOrConcurrentWithStartOf: v3_ActRelationshipType_EndsAfterOrConcurrentWithStartOf,
  /**
   * The source Act ends before the end of the target Act (i.e. if we say "ActOne EBE ActTwo", it means that ActOne ends before the end of ActTwo, therefore ActOne is the source and ActTwo is the target).
   *
   *
   *                            UsageNote: Inverse code is EAE
   */
  EndsBeforeEndOf: v3_ActRelationshipType_EndsBeforeEndOf,
  /**
   * A relationship in which the source act's effective time ends before or concurrent with the end of the target act's effective time.
   *
   *
   *                            Usage Note: Inverse code is EAEORECW
   */
  EndsBeforeOrConcurrentWithEndOf: v3_ActRelationshipType_EndsBeforeOrConcurrentWithEndOf,
  /**
   * A relationship in which the source act ends before the target act starts.
   *
   *
   *                            UsageNote: Inverse code is SAE
   */
  EndsBeforeStartOf: v3_ActRelationshipType_EndsBeforeStartOf,
  /**
   * A relationship in which the source act's effective time ends before or concurrent with the start of the target act's effective time.
   *
   *
   *                            Usage Note: Inverse code is EASORECWS
   */
  EndsBeforeOrConcurrentWithStartOf: v3_ActRelationshipType_EndsBeforeOrConcurrentWithStartOf,
  /**
   * A relationship in which the source act's effective time ends with the end of the target act's effective time.
   *
   *
   *                            UsageNote: This code is reflexive.  Therefore its inverse code is itself.
   */
  EndsConcurrentWith: v3_ActRelationshipType_EndsConcurrentWith,
  /**
   * The source Act ends when the target act starts (i.e. if we say "ActOne ECWS ActTwo", it means that ActOne ends when ActTwo starts, therefore ActOne is the source and ActTwo is the target).
   *
   *
   *                            UsageNote: Inverse code is SCWE
   */
  EndsConcurrentWithStartOf: v3_ActRelationshipType_EndsConcurrentWithStartOf,
  /**
   * A relationship in which the source act ends within the target act's effective time (including end points, as defined in the act's effective times)
   *
   *
   *                            UsageNote: Inverse code is SBEEAE
   */
  EndsDuring: v3_ActRelationshipType_EndsDuring,
  /**
   * Expresses an association that links two instances of the same act over time, indicating that the instance are part of the same episode, e.g. linking two condition nodes for episode of illness; linking two encounters for episode of encounter.
   */
  EpisodeLink: v3_ActRelationshipType_EpisodeLink,
  /**
   * A relationship in which the source act's effective time ends near the end of the target act's effective time. Near is defined separately as a time interval.
   *
   *
   *                            Usage Note: Inverse code is ENS
   */
  EndsNearEnd: v3_ActRelationshipType_EndsNearEnd,
  /**
   * A relationship in which the source act's effective time ends near the start of the target act's effective time. Near is defined separately as a time interval.
   *
   *
   *                            Usage Note: Inverse code is ENE
   */
  EndsNearStart: v3_ActRelationshipType_EndsNearStart,
  /**
   * Indicates that the target Act provides evidence in support of the action represented by the source Act. The target is not a 'reason' for the source act, but rather gives supporting information on why the source act is an appropriate course of action. Possible targets might be clinical trial results, journal articles, similar successful therapies, etc.
   *
   *
   *                            Rationale: Provides a mechanism for conveying clinical justification for non-approved or otherwise non-traditional therapies.
   */
  ProvidesEvidenceFor: v3_ActRelationshipType_ProvidesEvidenceFor,
  /**
   * Description:The source act is aggravated by the target act. (Example "chest pain" EXACBY "exercise")
   */
  ExacerbatedBy: v3_ActRelationshipType_ExacerbatedBy,
  /**
   * This is the inversion of support.  Used to indicate that a given observation is explained by another observation or condition.
   */
  HasExplanation: v3_ActRelationshipType_HasExplanation,
  /**
   * The source act fulfills (in whole or in part) the target act. Source act must be in a mood equal or more actual than the target act.
   */
  Fulfills: v3_ActRelationshipType_Fulfills,
  /**
   * The generalization relationship can be used to express categorical knowledge about services (e.g., amilorid, triamterene, and spironolactone have the common generalization potassium sparing diuretic).
   */
  HasGeneralization: v3_ActRelationshipType_HasGeneralization,
  /**
   * A goal-evaluation links an observation (intent or actual) to a goal to indicate that the observation evaluates the goal. Given the goal and the observation, a "goal distance" (e.g., goal to observation) can be "calculated" and need not be sent explicitly.
   */
  EvaluatesGoal: v3_ActRelationshipType_EvaluatesGoal,
  /**
   * A goal that one defines given a patient's health condition.  Subsequently planned actions aim to meet that goal.  Source is an observation or condition node, target must be an observation in goal mood.
   */
  HasGoal: v3_ActRelationshipType_HasGoal,
  /**
   * Description: The source act is intented to provide immunity against the effects of the target act (the target act describes an infectious disease)
   */
  ImmunizationAgainst: v3_ActRelationshipType_ImmunizationAgainst,
  /**
   * The source Act is independent of the time of the target Act.
   *
   *
   *                            UsageNote: This code is reflexive.  Therefore its inverse code is itself.
   */
  IndependentOfTimeOf: v3_ActRelationshipType_IndependentOfTimeOf,
  /**
   * Used to capture the link between a potential service ("master" or plan) and an actual service, where the actual service instantiates the potential service. The instantiation may override the master's defaults.
   */
  InstantiatesMaster: v3_ActRelationshipType_InstantiatesMaster,
  /**
   * the target act documents a set of circumstances (events, risks) which prevent successful completion, or degradation of quality of, the source Act.
   *
   *
   *                            UsageNote: This provides the semantics to document barriers to care
   */
  InterferedBy: v3_ActRelationshipType_InterferedBy,
  /**
   * Items located
   */
  ItemsLocated: v3_ActRelationshipType_ItemsLocated,
  /**
   * A relationship that limits or restricts the source act by the elements of the target act.  For example, an authorization may be limited by a financial amount (up to $500). Target Act must be in EVN.CRIT mood.
   */
  LimitedBy: v3_ActRelationshipType_LimitedBy,
  /**
   * The target Acts are aggregated by the source Act.  Target Acts may have independent existence, participate in multiple ActRelationships, and do not contribute to the meaning of the source.
   *
   *
   *                            UsageNotes: This explicitly represents the conventional notion of aggregation.  The target Act is part of a collection of Acts (no implication is made of cardinality, a source of Acts may contain zero, one, or more member target Acts).
   *
   *                         It is expected that this will be primarily used with _ActClassRecordOrganizer, BATTERY, and LIST
   */
  HasMember: v3_ActRelationshipType_HasMember,
  /**
   * Definition: Indicates that the attributes and associations of the target act provide metadata (for example, identifiers, authorship, etc.) for the source act.
   *
   *
   *                            Constraint:  Source act must have either a mood code that is not "EVN" (event) or its "isCriterion" attribute must set to "true".  Target act must be an Act with a mood code of EVN and with isCriterionInd attribute set to "true".
   */
  HasMetadata: v3_ActRelationshipType_HasMetadata,
  /**
   * An assertion that a new observation may be the manifestation of another existing observation or action.  This assumption is attributed to the same actor who asserts the manifestation.  This is stronger and more specific than an inverted support link.  For example, an agitated appearance can be asserted to be the manifestation (effect) of a known hyperthyroxia.  This expresses that one might not have realized a symptom if it would not be a common manifestation of a known condition.  The target (cause) may be any service, while the source (manifestation) must be an observation.
   */
  IsManifestationOf: v3_ActRelationshipType_IsManifestationOf,
  /**
   * The source act removes or lessens the occurrence or effect of the target act.
   */
  Mitigates: v3_ActRelationshipType_Mitigates,
  /**
   * Definition: Used to link a newer version or 'snapshot' of a business object (source) to an older version or 'snapshot' of the same business object (target).
   *
   *
   *                            Usage:The identifier of the Act should be the same for both source and target. If the identifiers are distinct, RPLC should be used instead.
   *
   *                         Name from source to target = "modifiesPrior"
   *
   *                         Name from target to source = "modifiesByNew"
   */
  Modifies: v3_ActRelationshipType_Modifies,
  /**
   * A trigger-match links an actual service (e.g., an observation or procedure that took place) with a service in criterion mood.  For example if the trigger is "observation of pain" and pain is actually observed, and if that pain-observation caused the trigger to fire, that pain-observation can be linked with the trigger.
   */
  MatchesTrigger: v3_ActRelationshipType_MatchesTrigger,
  /**
   * adjunct mitigation
   */
  AdjunctMitigation: v3_ActRelationshipType_AdjunctMitigation,
  /**
   * Description: The source act is intended to provide long term maintenance improvement or management of a pre-existing adverse situation described by the target act. This is not limited to diseases but can apply to any adverse situation or condition of medical or technical nature.
   */
  MaintenanceTreatment: v3_ActRelationshipType_MaintenanceTreatment,
  /**
   * Used to assign a "name" to a condition thread. Source is a condition node, target can be any service.
   */
  AssignsName: v3_ActRelationshipType_AssignsName,
  /**
   * A desired state that a service action aims to maintain.  E.g., keep systolic blood pressure between 90 and 110 mm Hg.  Source is an intervention service.  Target must be an observation in criterion mood.
   */
  HasContinuingObjective: v3_ActRelationshipType_HasContinuingObjective,
  /**
   * A desired outcome that a service action aims to meet finally.  Source is any service (typically an intervention).  Target must be an observation in criterion mood.
   */
  HasFinalObjective: v3_ActRelationshipType_HasFinalObjective,
  /**
   * The source act is a single occurrence of a repeatable target act. The source and target act can be in any mood on the "completion track" but the source act must be as far as or further along the track than the target act (i.e., the occurrence of an intent can be an event but not vice versa).
   */
  Occurrence: v3_ActRelationshipType_Occurrence,
  /**
   * A relationship between a source Act that provides more detailed properties to the target Act.
   *
   *                         The source act thus is a specialization of the target act, but instead of mentioning all the inherited properties it only mentions new property bindings or refinements.
   *
   *                         The typical use case is to specify certain alternative variants of one kind of Act. The priorityNumber attribute is used to weigh refinements as preferred over other alternative refinements.
   *
   *                         Example: several routing options for a drug are specified as one SubstanceAdministration for the general treatment with attached refinements for the various routing options.
   */
  HasOption: v3_ActRelationshipType_HasOption,
  /**
   * Relates either an appointment request or an appointment to the order for the service being scheduled.
   */
  ReferencesOrder: v3_ActRelationshipType_ReferencesOrder,
  /**
   * An observation that should follow or does actually follow as a result or consequence of a condition or action (sometimes called "post-conditional".) Target must be an observation as a goal, risk or any criterion. For complex outcomes a conjunction attribute (AND, OR, XOR) can be used.  An outcome link is often inverted to describe an outcome assessment.
   */
  HasOutcome: v3_ActRelationshipType_HasOutcome,
  /**
   * A relationship in which the source act's effective time overlaps the target act's effective time in any way.
   *
   *
   *                            UsageNote: This code is reflexive.  Therefore its inverse code is itself.
   */
  OverlapsWith: v3_ActRelationshipType_OverlapsWith,
  /**
   * Description: The source act is intended to provide palliation for the effects of the target act.
   */
  Palliates: v3_ActRelationshipType_Palliates,
  /**
   * The source Act is a composite of the target Acts. The target Acts do not have an existence independent of the source Act.
   *
   *
   *                            UsageNote: In UML 1.1, this is a "composition" defined as:
   *                            "A form of aggregation with strong ownership and coincident lifetime as part of the whole. Parts with non-fixed multiplicity may be created after the composite itself, but once created they live and die with it (i.e., they share lifetimes). Such parts can also be explicitly removed before the death of the composite. Composition may be recursive."
   */
  HasPart: v3_ActRelationshipType_HasPart,
  /**
   * Description: The source act is intended to provide passive immunity against the effects of the target act (the target act describes an infectious disease).
   */
  PassiveImmunizationAgainst: v3_ActRelationshipType_PassiveImmunizationAgainst,
  /**
   * This is a very unspecific relationship from one item of clinical information to another.  It does not judge about the role the pertinent information plays.
   */
  HasPertinentInformation: v3_ActRelationshipType_HasPertinentInformation,
  /**
   * A requirement to be true before a service is performed. The target can be any service in criterion mood.  For multiple pre-conditions a conjunction attribute (AND, OR, XOR) is applicable.
   */
  HasPreCondition: v3_ActRelationshipType_HasPreCondition,
  /**
   * A relationship in which the target act is a predecessor instance to the source act.  Generally each of these instances is similar, but no identical.  In healthcare coverage it is used to link a claim item to a previous claim item that might have claimed for the same set of services.
   */
  HasPreviousInstance: v3_ActRelationshipType_HasPreviousInstance,
  /**
   * Description: The source act is intended to reduce the risk of of an adverse situation to emerge as described by the target act. This is not limited to diseases but can apply to any adverse situation or condition of medical or technical nature.
   */
  ProphylaxisOf: v3_ActRelationshipType_ProphylaxisOf,
  /**
   * The target observation qualifies (refines) the semantics of the source observation.
   *
   *
   *                            UsageNote: This is not intended to replace concept refinement and qualification via vocabulary.  It is used when there are multiple components which together provide the complete understanding of the source Act.
   */
  HasQualifier: v3_ActRelationshipType_HasQualifier,
  RACT: v3_ActRelationshipType_RACT,
  /**
   * Description:A relationship in which the target act is carried out to determine whether an effect attributed to the source act can be recreated.
   */
  ReChallenge: v3_ActRelationshipType_ReChallenge,
  /**
   * Definition: The source act is performed to recover from the effects of the target act.
   */
  Recovers: v3_ActRelationshipType_Recovers,
  /**
   * A relationship in which the target act is referred to by the source act.  This permits a simple reference relationship that distinguishes between the referent and the referee.
   */
  RefersTo: v3_ActRelationshipType_RefersTo,
  /**
   * Reference ranges are essentially descriptors of a class of result values assumed to be "normal", "abnormal", or "critical."  Those can vary by sex, age, or any other criterion. Source and target are observations, the target is in criterion mood.  This link type can act as a trigger in case of alarms being triggered by critical results.
   */
  HasReferenceValues: v3_ActRelationshipType_HasReferenceValues,
  /**
   * Description:The source act is wholly or partially alleviated by the target act. (Example "chest pain" RELVBY "sublingual nitroglycerin administration")
   */
  RelievedBy: v3_ActRelationshipType_RelievedBy,
  /**
   * A relationship between a source Act that seeks to reverse or undo the action of the prior target Act.
   *
   *                         Example: A posted financial transaction (e.g., a debit transaction) was applied in error and must be reversed (e.g., by a credit transaction) the credit transaction is identified as an undo (or reversal) of the prior target transaction.
   *
   *                         Constraints: the "completion track" mood of the target Act must be equally or more "actual" than the source act. I.e., when the target act is EVN the source act can be EVN, or any INT. If the target act is INT, the source act can be INT.
   */
  Reverses: v3_ActRelationshipType_Reverses,
  /**
   * A noteworthy undesired outcome of a patient's condition that is either likely enough to become an issue or is less likely but dangerous enough to be addressed.
   */
  HasRisk: v3_ActRelationshipType_HasRisk,
  /**
   * A replacement source act replaces an existing target act. The state of the target act being replaced becomes obselete, but the act is typically still retained in the system for historical reference.  The source and target must be of the same type.
   */
  Replaces: v3_ActRelationshipType_Replaces,
  /**
   * Description: The reason or rationale for a service. A reason link is weaker than a trigger, it only suggests that some service may be or might have been a reason for some action, but not that this reason requires/required the action to be taken. Also, as opposed to the trigger, there is no strong timely relation between the reason and the action.  As well as providing various types of information about the rationale for a service, the RSON act relationship is routinely used between a SBADM act and an OBS act to describe the indication for use of a medication.  Child concepts may be used to describe types of indication.
   *
   *
   *                            Discussion: In prior releases, the code "SUGG" (suggests) was expressed as "an inversion of the reason link." That code has been retired in favor of the inversion indicator that is an attribute of ActRelationship.
   */
  HasReason: v3_ActRelationshipType_HasReason,
  /**
   * A relationship in which the source act starts after the target act ends.
   *
   *
   *                            UsageNote: Inverse code is EBS
   */
  StartsAfterEndOf: v3_ActRelationshipType_StartsAfterEndOf,
  /**
   * A relationship in which the source act's effective time starts after or concurrent with the end of the target act's effective time.
   *
   *
   *                            Usage Note: Inverse code is SBEORSCWE
   */
  StartsAfterOrConcurrentWithEndOf: v3_ActRelationshipType_StartsAfterOrConcurrentWithEndOf,
  /**
   * The source Act starts after the start of the target Act (i.e. if we say "ActOne SAS ActTwo", it means that ActOne starts after the start of ActTwo, therefore ActOne is the source and ActTwo is the target).
   *
   *
   *                            UsageNote: Inverse code is SBS
   */
  StartsAfterStartOf: v3_ActRelationshipType_StartsAfterStartOf,
  /**
   * The source Act starts after start of the target Act and ends after end of the target Act.
   *
   *
   *                            UsageNote: Inverse code is SBSEBE
   */
  StartsAfterStartOfEndsAfterEndOf: v3_ActRelationshipType_StartsAfterStartOfEndsAfterEndOf,
  /**
   * The source Act starts after start of the target Act, and ends with the target Act.
   *
   *
   *                            UsageNote: Inverse code is SBSECWE
   */
  StartsAfterStartOfEndsWith: v3_ActRelationshipType_StartsAfterStartOfEndsWith,
  /**
   * A relationship in which the source act's effective time starts after or concurrent with the start of the target act's effective time.
   *
   *
   *                            Usage Note: Inverse code is SBSORSCW
   */
  StartsAfterOrConcurrentWithStartOf: v3_ActRelationshipType_StartsAfterOrConcurrentWithStartOf,
  /**
   * The source Act start after the start of the target Act, and contains the end of the target Act.
   *
   *
   *                            UsageNote: Inverse code is SBSEASEBE
   */
  StartAfterStartOfContainsEndOf: v3_ActRelationshipType_StartAfterStartOfContainsEndOf,
  /**
   * The source Act starts before the end of the target Act (i.e. if we say "ActOne SBE ActTwo", it means that ActOne starts before the end of ActTwo, therefore ActOne is the source and ActTwo is the target).
   *
   *
   *                            UsageNote: Inverse code is EAS
   */
  StartsBeforeEndOf: v3_ActRelationshipType_StartsBeforeEndOf,
  /**
   * The source Act contains the end of the target Act.
   *
   *
   *                            UsageNote: Inverse code is EDU
   */
  ContainsEndOf: v3_ActRelationshipType_ContainsEndOf,
  /**
   * A relationship in which the source act's effective time starts before or concurrent with the end of the target act's effective time.
   *
   *
   *                            Usage Note: Inverse code is SAEORSCWE
   */
  StartsBeforeOrConcurrentWithEndOf: v3_ActRelationshipType_StartsBeforeOrConcurrentWithEndOf,
  /**
   * A relationship in which the source act begins before the target act begins.
   *
   *
   *                            UsageNote: Inverse code is SAS
   */
  StartsBeforeStartOf: v3_ActRelationshipType_StartsBeforeStartOf,
  /**
   * The source Act contains the time of the target Act.
   *
   *
   *                            UsageNote: Inverse code is DURING
   */
  ContainsTimeOf: v3_ActRelationshipType_ContainsTimeOf,
  /**
   * The source Act contains the start of the target Act.
   *
   *
   *                            UsageNote: Inverse code is SDU
   */
  ContainsStartOf: v3_ActRelationshipType_ContainsStartOf,
  /**
   * The source Act contains the start of the target Act,  and ends before the end of the target Act.
   *
   *
   *                            UsageNote: Inverse code is SASSBEEAS
   */
  ContainsStartOfEndsBeforeEndOf: v3_ActRelationshipType_ContainsStartOfEndsBeforeEndOf,
  /**
   * The source Act starts before the start of the target Act, and ends before the end of the target Act.
   *
   *
   *                            UsageNote: Inverse code is SASEAE
   */
  StartsBeforeStartOfEndsBeforeEndOf: v3_ActRelationshipType_StartsBeforeStartOfEndsBeforeEndOf,
  /**
   * The source Act starts before the start of the target Act, and ends with the target Act.
   *
   *
   *                            UsageNote: Inverse code is SASECWE
   */
  StartsBeforeStartOfEndsWith: v3_ActRelationshipType_StartsBeforeStartOfEndsWith,
  /**
   * A relationship in which the source act's effective time starts before or concurrent with the start of the target act's effective time.
   *
   *
   *                            Usage Note: Inverse code is SASORSCW
   */
  StartsBeforeOrConcurrentWithStartOf: v3_ActRelationshipType_StartsBeforeOrConcurrentWithStartOf,
  /**
   * Associates a specific time (and associated resources) with a scheduling request or other intent.
   */
  SchedulesRequest: v3_ActRelationshipType_SchedulesRequest,
  /**
   * A relationship in which the source act's effective time starts with the start of the target act's effective time.
   *
   *
   *                            UsageNote: This code is reflexive.  Therefore its inverse code is itself.
   */
  StartsConcurrentWith: v3_ActRelationshipType_StartsConcurrentWith,
  /**
   * The source Act starts when the target act ends (i.e. if we say "ActOne SCWE ActTwo", it means that ActOne starts when ActTwo ends, therefore ActOne is the source and ActTwo is the target).
   *
   *
   *                            UsageNote: Inverse code is SBSECWS
   */
  StartsConcurrentWithEndOf: v3_ActRelationshipType_StartsConcurrentWithEndOf,
  /**
   * The source Act starts with the target Act, and ends after the end of the target Act.
   */
  StartsWithEndsAfterEndOf: v3_ActRelationshipType_StartsWithEndsAfterEndOf,
  /**
   * The source Act starts with.the target Act and ends before the end of the target Act.
   *
   *
   *                            UsageNote: Inverse code is SCWSEAE
   */
  StartsWithEndsBeforeEndOf: v3_ActRelationshipType_StartsWithEndsBeforeEndOf,
  /**
   * A relationship in which the source act starts within the target act's effective time (including end points, as defined in the act's effective times)
   *
   *
   *                            UsageNote: Inverse code is SBSEAS
   */
  StartsDuring: v3_ActRelationshipType_StartsDuring,
  /**
   * An act relationship indicating that the source act follows the target act. The source act should in principle represent the same kind of act as the target. Source and target need not have the same mood code (mood will often differ). The target of a sequel is called antecedent. Examples for sequel relationships are: revision, transformation, derivation from a prototype (as a specialization is a derivation of a generalization), followup, realization, instantiation.
   */
  IsSequel: v3_ActRelationshipType_IsSequel,
  /**
   * A relationship in which the source act's effective time starts near the end of the target act's effective time. Near is defined separately as a time interval.
   *
   *
   *                            Usage Note: Inverse code is SNS
   */
  StartsNearEnd: v3_ActRelationshipType_StartsNearEnd,
  /**
   * A relationship in which the source act's effective time starts near the start of the target act's effective time. Near is defined separately as a time interval.
   *
   *
   *                            Usage Note: Inverse code is SNE
   */
  StartsNearStart: v3_ActRelationshipType_StartsNearStart,
  /**
   * Used to indicate that an existing service is suggesting evidence for a new observation. The assumption of support is attributed to the same actor who asserts the observation. Source must be an observation, target may be any service  (e.g., to indicate a status post).
   */
  HasSupport: v3_ActRelationshipType_HasSupport,
  /**
   * A specialization of "has support" (SPRT), used to relate a secondary observation to a Region of Interest on a multidimensional observation, if the ROI specifies the true boundaries of the secondary observation as opposed to only marking the approximate area.  For example, if the start and end of an ST elevation episode is visible in an EKG, this relation would indicate the ROI bounds the  "ST elevation" observation -- the ROI defines the true beginning and ending of the episode.  Conversely, if a ROI simply contains ST elevation, but it does not define the bounds (start and end) of the episode, the more general "has support" relation is used.  Likewise, if a ROI on an image defines the true bounds of a "1st degree burn", the relation "has bounded support" is used; but if the ROI only points to the approximate area of the burn, the general "has support" relation is used.
   */
  HasBoundedSupport: v3_ActRelationshipType_HasBoundedSupport,
  /**
   * A collection of sub-services as steps or subtasks performed for the source service. Services may be performed sequentially or concurrently.
   *
   *
   *                            UsageNotes: Sequence of steps may be indicated by use of _ActRelationshipTemporallyPertains, as well as via  ActRelationship.sequenceNumber, ActRelationship.pauseQuantity, Target.priorityCode.
   *
   *
   *                            OpenIssue: Need Additional guidelines on when each approach should be used.
   */
  HasStep: v3_ActRelationshipType_HasStep,
  /**
   * Relates an Act to its subject Act that the first Act is primarily concerned with.
   *
   *                         Examples
   *
   *
   *
   *                               The first Act may be a ControlAct manipulating the subject Act
   *
   *
   *
   *                               The first act is a region of interest (ROI) that defines a region within the subject Act.
   *
   *
   *
   *                               The first act is a reporting or notification Act, that echos the subject Act for a specific new purpose.
   *
   *
   *
   *                         Constraints
   *
   *                         An Act may have multiple subject acts.
   *
   *                         Rationale
   *
   *                         The ActRelationshipType "has subject" is similar to the ParticipationType "subject", Acts that primarily operate on physical subjects use the Participation, those Acts that primarily operate on other Acts (other information) use the ActRelationship.
   */
  HasSubject: v3_ActRelationshipType_HasSubject,
  /**
   * Definition:  A new act that carries forward the intention of the original act, but does not completely replace it.  The status of the predecessor act must be 'completed'.  The original act is the target act and the successor is the source act.
   */
  Succeeds: v3_ActRelationshipType_Succeeds,
  SUGG: v3_ActRelationshipType_SUGG,
  /**
   * An act that contains summary values for a list or set of subordinate acts.  For example, a summary of transactions for a particular accounting period.
   */
  SummarizedBy: v3_ActRelationshipType_SummarizedBy,
  /**
   * Description: The source act is intented to provide symptomatic relief for the effects of the target act.
   */
  SymptomaticRelief: v3_ActRelationshipType_SymptomaticRelief,
  /**
   * Description: The source act is intended to improve a pre-existing adverse situation described by the target act. This is not limited to diseases but can apply to any adverse situation or condition of medical or technical nature.
   */
  Treats: v3_ActRelationshipType_Treats,
  /**
   * A pre-condition that if true should result in the source Act being executed.  The target is in typically in criterion mood.  When reported after the fact (i.e. the criterion has been met) it may be in Event mood.  A delay between the trigger and the triggered action can be specified.
   *
   *
   *                            Discussion: This includes the concept of a  required act for a service or financial instrument such as an insurance plan or policy. In such cases, the trigger is the occurrence of a specific condition such as coverage limits being exceeded.
   */
  HasTrigger: v3_ActRelationshipType_HasTrigger,
  /**
   * A condition thread relationship specifically links condition nodes together to form a condition thread. The source is the new condition node and the target links to the most recent node of the existing condition thread.
   */
  UpdatesCondition: v3_ActRelationshipType_UpdatesCondition,
  /**
   * Indicates that the source act makes use of (or will make use of) the information content of the target act.
   *
   *
   *                            UsageNotes: A usage relationship only makes sense if the target act is authored and occurs independently of the source act.  Otherwise a simpler relationship such as COMP would be appropriate.
   *
   *
   *                            Rationale: There is a need when defining a clinical trial protocol to indicate that the protocol makes use of other protocol or treatment specifications.  This is stronger than the assertion of "references".  References may exist without usage, and in a clinical trial protocol is common to assert both: what other specifications does this trial use and what other specifications does it merely reference.
   */
  Uses: v3_ActRelationshipType_Uses,
  /**
   * Description:Indicates that the target Act represents the result of the source observation Act.
   *
   *
   *                            FormalConstraint: Source Act must be an Observation or specialization there-of. Source Act must not have the value attribute specified
   *
   *
   *                            UsageNote: This relationship allows the result of an observation to be fully expressed as RIM acts as opposed to being embedded in the value attribute.  For example, sending a Document act as the result of an imaging observation, sending a list of Procedures and/or other acts as the result of a medical history observation.
   *
   *                         The valueNegationInd attribute on the source Act has the same semantics of "negated finding" when it applies to the target of a VALUE ActRelationship as it does to the value attribute.  On the other hand, if the ActRelationship.negationInd is true for a VALUE ActRelationship, that means the specified observation does not have the indicated value but does not imply a negated finding.  Because the semantics are extremely close, it is recommended that Observation.valueNegationInd be used, not ActRelationship.negationInd.
   *
   *
   *                            OpenIssue: The implications of negationInd on ActRelationship and the valueNegationind on Observation.
   */
  HasValue: v3_ActRelationshipType_HasValue,
  /**
   * The source is a direct quote from the target.
   */
  ExcerptVerbatim: v3_ActRelationshipType_ExcerptVerbatim,
  /**
   * The source is an excerpt from the target.
   */
  Excerpts: v3_ActRelationshipType_Excerpts,
  /**
   * Used when the target Act is a transformation of the source Act. (For instance, used to show that a CDA document is a transformation of a DICOM SR document.)
   */
  Transformation: v3_ActRelationshipType_Transformation,
};
const v3_ActSite_HumanActSite: Coding = {
  code: '_HumanActSite',
  display: 'HumanActSite',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_HumanSubstanceAdministrationSite: Coding = {
  code: '_HumanSubstanceAdministrationSite',
  display: 'HumanSubstanceAdministrationSite',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_BilateralEars: Coding = {
  code: 'BE',
  display: 'bilateral ears',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_BilateralNares: Coding = {
  code: 'BN',
  display: 'bilateral nares',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_Buttock: Coding = {
  code: 'BU',
  display: 'buttock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftAnteriorChest: Coding = {
  code: 'LAC',
  display: 'left anterior chest',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftAntecubitalFossa: Coding = {
  code: 'LACF',
  display: 'left antecubital fossa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftDeltoid: Coding = {
  code: 'LD',
  display: 'left deltoid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftEar: Coding = {
  code: 'LE',
  display: 'left ear',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftExternalJugular: Coding = {
  code: 'LEJ',
  display: 'left external jugular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftFoot: Coding = {
  code: 'LF',
  display: 'left foot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftGluteusMedius: Coding = {
  code: 'LG',
  display: 'left gluteus medius',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftHand: Coding = {
  code: 'LH',
  display: 'left hand',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftInternalJugular: Coding = {
  code: 'LIJ',
  display: 'left internal jugular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftLowerAbdQuadrant: Coding = {
  code: 'LLAQ',
  display: 'left lower abd quadrant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftLowerForearm: Coding = {
  code: 'LLFA',
  display: 'left lower forearm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftMidForearm: Coding = {
  code: 'LMFA',
  display: 'left mid forearm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftNaris: Coding = {
  code: 'LN',
  display: 'left naris',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftPosteriorChest: Coding = {
  code: 'LPC',
  display: 'left posterior chest',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftSubclavian: Coding = {
  code: 'LSC',
  display: 'left subclavian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftThigh: Coding = {
  code: 'LT',
  display: 'left thigh',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftUpperArm: Coding = {
  code: 'LUA',
  display: 'left upper arm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftUpperAbdQuadrant: Coding = {
  code: 'LUAQ',
  display: 'left upper abd quadrant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftUpperForearm: Coding = {
  code: 'LUFA',
  display: 'left upper forearm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftVentragluteal: Coding = {
  code: 'LVG',
  display: 'left ventragluteal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftVastusLateralis: Coding = {
  code: 'LVL',
  display: 'left vastus lateralis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightEye: Coding = {
  code: 'OD',
  display: 'right eye',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_LeftEye: Coding = {
  code: 'OS',
  display: 'left eye',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_BilateralEyes: Coding = {
  code: 'OU',
  display: 'bilateral eyes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_Perianal: Coding = {
  code: 'PA',
  display: 'perianal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_Perineal: Coding = {
  code: 'PERIN',
  display: 'perineal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightAnteriorChest: Coding = {
  code: 'RAC',
  display: 'right anterior chest',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightAntecubitalFossa: Coding = {
  code: 'RACF',
  display: 'right antecubital fossa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightDeltoid: Coding = {
  code: 'RD',
  display: 'right deltoid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightEar: Coding = {
  code: 'RE',
  display: 'right ear',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightExternalJugular: Coding = {
  code: 'REJ',
  display: 'right external jugular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightFoot: Coding = {
  code: 'RF',
  display: 'right foot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightGluteusMedius: Coding = {
  code: 'RG',
  display: 'right gluteus medius',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightHand: Coding = {
  code: 'RH',
  display: 'right hand',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightInternalJugular: Coding = {
  code: 'RIJ',
  display: 'right internal jugular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightLowerAbdQuadrant: Coding = {
  code: 'RLAQ',
  display: 'right lower abd quadrant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightLowerForearm: Coding = {
  code: 'RLFA',
  display: 'right lower forearm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightMidForearm: Coding = {
  code: 'RMFA',
  display: 'right mid forearm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightNaris: Coding = {
  code: 'RN',
  display: 'right naris',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightPosteriorChest: Coding = {
  code: 'RPC',
  display: 'right posterior chest',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightSubclavian: Coding = {
  code: 'RSC',
  display: 'right subclavian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightThigh: Coding = {
  code: 'RT',
  display: 'right thigh',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightUpperArm: Coding = {
  code: 'RUA',
  display: 'right upper arm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightUpperAbdQuadrant: Coding = {
  code: 'RUAQ',
  display: 'right upper abd quadrant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightUpperForearm: Coding = {
  code: 'RUFA',
  display: 'right upper forearm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightVentragluteal: Coding = {
  code: 'RVG',
  display: 'right ventragluteal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
const v3_ActSite_RightVastusLateralis: Coding = {
  code: 'RVL',
  display: 'right vastus lateralis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
};
/**
 *  An anatomical location on an organism which can be the focus of an act.
 */
export const V3ActSite = {
  /**
   * An anatomical location on a human which can be the focus of an act.
   */
  HumanActSite: v3_ActSite_HumanActSite,
  /**
   * The set of body locations to or through which a drug product may be administered.
   */
  HumanSubstanceAdministrationSite: v3_ActSite_HumanSubstanceAdministrationSite,
  /**
   * bilateral ears
   */
  BilateralEars: v3_ActSite_BilateralEars,
  /**
   * bilateral nares
   */
  BilateralNares: v3_ActSite_BilateralNares,
  /**
   * buttock
   */
  Buttock: v3_ActSite_Buttock,
  /**
   * left arm
   */
  LeftArm: v3_ActSite_LeftArm,
  /**
   * left anterior chest
   */
  LeftAnteriorChest: v3_ActSite_LeftAnteriorChest,
  /**
   * left antecubital fossa
   */
  LeftAntecubitalFossa: v3_ActSite_LeftAntecubitalFossa,
  /**
   * left deltoid
   */
  LeftDeltoid: v3_ActSite_LeftDeltoid,
  /**
   * left ear
   */
  LeftEar: v3_ActSite_LeftEar,
  /**
   * left external jugular
   */
  LeftExternalJugular: v3_ActSite_LeftExternalJugular,
  /**
   * left foot
   */
  LeftFoot: v3_ActSite_LeftFoot,
  /**
   * left gluteus medius
   */
  LeftGluteusMedius: v3_ActSite_LeftGluteusMedius,
  /**
   * left hand
   */
  LeftHand: v3_ActSite_LeftHand,
  /**
   * left internal jugular
   */
  LeftInternalJugular: v3_ActSite_LeftInternalJugular,
  /**
   * left lower abd quadrant
   */
  LeftLowerAbdQuadrant: v3_ActSite_LeftLowerAbdQuadrant,
  /**
   * left lower forearm
   */
  LeftLowerForearm: v3_ActSite_LeftLowerForearm,
  /**
   * left mid forearm
   */
  LeftMidForearm: v3_ActSite_LeftMidForearm,
  /**
   * left naris
   */
  LeftNaris: v3_ActSite_LeftNaris,
  /**
   * left posterior chest
   */
  LeftPosteriorChest: v3_ActSite_LeftPosteriorChest,
  /**
   * left subclavian
   */
  LeftSubclavian: v3_ActSite_LeftSubclavian,
  /**
   * left thigh
   */
  LeftThigh: v3_ActSite_LeftThigh,
  /**
   * left upper arm
   */
  LeftUpperArm: v3_ActSite_LeftUpperArm,
  /**
   * left upper abd quadrant
   */
  LeftUpperAbdQuadrant: v3_ActSite_LeftUpperAbdQuadrant,
  /**
   * left upper forearm
   */
  LeftUpperForearm: v3_ActSite_LeftUpperForearm,
  /**
   * left ventragluteal
   */
  LeftVentragluteal: v3_ActSite_LeftVentragluteal,
  /**
   * left vastus lateralis
   */
  LeftVastusLateralis: v3_ActSite_LeftVastusLateralis,
  /**
   * right eye
   */
  RightEye: v3_ActSite_RightEye,
  /**
   * left eye
   */
  LeftEye: v3_ActSite_LeftEye,
  /**
   * bilateral eyes
   */
  BilateralEyes: v3_ActSite_BilateralEyes,
  /**
   * perianal
   */
  Perianal: v3_ActSite_Perianal,
  /**
   * perineal
   */
  Perineal: v3_ActSite_Perineal,
  /**
   * right arm
   */
  RightArm: v3_ActSite_RightArm,
  /**
   * right anterior chest
   */
  RightAnteriorChest: v3_ActSite_RightAnteriorChest,
  /**
   * right antecubital fossa
   */
  RightAntecubitalFossa: v3_ActSite_RightAntecubitalFossa,
  /**
   * right deltoid
   */
  RightDeltoid: v3_ActSite_RightDeltoid,
  /**
   * right ear
   */
  RightEar: v3_ActSite_RightEar,
  /**
   * right external jugular
   */
  RightExternalJugular: v3_ActSite_RightExternalJugular,
  /**
   * right foot
   */
  RightFoot: v3_ActSite_RightFoot,
  /**
   * right gluteus medius
   */
  RightGluteusMedius: v3_ActSite_RightGluteusMedius,
  /**
   * right hand
   */
  RightHand: v3_ActSite_RightHand,
  /**
   * right internal jugular
   */
  RightInternalJugular: v3_ActSite_RightInternalJugular,
  /**
   * right lower abd quadrant
   */
  RightLowerAbdQuadrant: v3_ActSite_RightLowerAbdQuadrant,
  /**
   * right lower forearm
   */
  RightLowerForearm: v3_ActSite_RightLowerForearm,
  /**
   * right mid forearm
   */
  RightMidForearm: v3_ActSite_RightMidForearm,
  /**
   * right naris
   */
  RightNaris: v3_ActSite_RightNaris,
  /**
   * right posterior chest
   */
  RightPosteriorChest: v3_ActSite_RightPosteriorChest,
  /**
   * right subclavian
   */
  RightSubclavian: v3_ActSite_RightSubclavian,
  /**
   * right thigh
   */
  RightThigh: v3_ActSite_RightThigh,
  /**
   * right upper arm
   */
  RightUpperArm: v3_ActSite_RightUpperArm,
  /**
   * right upper abd quadrant
   */
  RightUpperAbdQuadrant: v3_ActSite_RightUpperAbdQuadrant,
  /**
   * right upper forearm
   */
  RightUpperForearm: v3_ActSite_RightUpperForearm,
  /**
   * right ventragluteal
   */
  RightVentragluteal: v3_ActSite_RightVentragluteal,
  /**
   * right vastus lateralis
   */
  RightVastusLateralis: v3_ActSite_RightVastusLateralis,
};
const v3_ActStatus_Aborted: Coding = {
  code: 'aborted',
  display: 'aborted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActStatus',
};
const v3_ActStatus_Active: Coding = {
  code: 'active',
  display: 'active',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActStatus',
};
const v3_ActStatus_Cancelled: Coding = {
  code: 'cancelled',
  display: 'cancelled',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActStatus',
};
const v3_ActStatus_Completed: Coding = {
  code: 'completed',
  display: 'completed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActStatus',
};
const v3_ActStatus_Held: Coding = {
  code: 'held',
  display: 'held',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActStatus',
};
const v3_ActStatus_New: Coding = {
  code: 'new',
  display: 'new',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActStatus',
};
const v3_ActStatus_Normal: Coding = {
  code: 'normal',
  display: 'normal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActStatus',
};
const v3_ActStatus_Nullified: Coding = {
  code: 'nullified',
  display: 'nullified',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActStatus',
};
const v3_ActStatus_Obsolete: Coding = {
  code: 'obsolete',
  display: 'obsolete',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActStatus',
};
const v3_ActStatus_Suspended: Coding = {
  code: 'suspended',
  display: 'suspended',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActStatus',
};
/**
 *  Codes representing the defined possible states of an Act, as defined by the Act class state machine.
 */
export const V3ActStatus = {
  /**
   * The Act has been terminated prior to the originally intended completion.
   */
  Aborted: v3_ActStatus_Aborted,
  /**
   * The Act can be performed or is being performed
   */
  Active: v3_ActStatus_Active,
  /**
   * The Act has been abandoned before activation.
   */
  Cancelled: v3_ActStatus_Cancelled,
  /**
   * An Act that has terminated normally after all of its constituents have been performed.
   */
  Completed: v3_ActStatus_Completed,
  /**
   * An Act that is still in the preparatory stages has been put aside.  No action can occur until the Act is released.
   */
  Held: v3_ActStatus_Held,
  /**
   * An Act that is in the preparatory stages and may not yet be acted upon
   */
  New: v3_ActStatus_New,
  /**
   * Encompasses the expected states of an Act, but excludes "nullified" and "obsolete" which represent unusual terminal states for the life-cycle.
   */
  Normal: v3_ActStatus_Normal,
  /**
   * This Act instance was created in error and has been 'removed' and is treated as though it never existed.  A record is retained for audit purposes only.
   */
  Nullified: v3_ActStatus_Nullified,
  /**
   * This Act instance has been replaced by a new instance.
   */
  Obsolete: v3_ActStatus_Obsolete,
  /**
   * An Act that has been activated (actions could or have been performed against it), but has been temporarily disabled.  No further action should be taken against it until it is released
   */
  Suspended: v3_ActStatus_Suspended,
};
const v3_substanceAdminSubstitution_BrandComposition: Coding = {
  code: 'BC',
  display: 'brand composition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-substanceAdminSubstitution',
};
const v3_substanceAdminSubstitution_Equivalent: Coding = {
  code: 'E',
  display: 'equivalent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-substanceAdminSubstitution',
};
const v3_substanceAdminSubstitution_EquivalentComposition: Coding = {
  code: 'EC',
  display: 'equivalent composition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-substanceAdminSubstitution',
};
const v3_substanceAdminSubstitution_Formulary: Coding = {
  code: 'F',
  display: 'formulary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-substanceAdminSubstitution',
};
const v3_substanceAdminSubstitution_GenericComposition: Coding = {
  code: 'G',
  display: 'generic composition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-substanceAdminSubstitution',
};
const v3_substanceAdminSubstitution_None: Coding = {
  code: 'N',
  display: 'none',
  system: 'http://terminology.hl7.org/CodeSystem/v3-substanceAdminSubstitution',
};
const v3_substanceAdminSubstitution_TherapeuticBrand: Coding = {
  code: 'TB',
  display: 'therapeutic brand',
  system: 'http://terminology.hl7.org/CodeSystem/v3-substanceAdminSubstitution',
};
const v3_substanceAdminSubstitution_TherapeuticAlternative: Coding = {
  code: 'TE',
  display: 'therapeutic alternative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-substanceAdminSubstitution',
};
const v3_substanceAdminSubstitution_TherapeuticGeneric: Coding = {
  code: 'TG',
  display: 'therapeutic generic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-substanceAdminSubstitution',
};
/**
 * No Description Provided
 */
export const V3ActSubstanceAdminSubstitutionCode = {
  /**
   * Description:
   *
   *
   *                         Substitution occurred or is permitted between equivalent Brands but not Generics
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Zestril  for Prinivil
   *                            Coumadin for Jantoven
   */
  BrandComposition: v3_substanceAdminSubstitution_BrandComposition,
  /**
   * Description: Substitution occurred or is permitted with another bioequivalent and therapeutically equivalent product.
   */
  Equivalent: v3_substanceAdminSubstitution_Equivalent,
  /**
   * Description:
   *
   *
   *                         Substitution occurred or is permitted with another product that is a:
   *
   *
   *                            pharmaceutical alternative containing the same active ingredient but is formulated with different salt, ester
   *                            pharmaceutical equivalent that has the same active ingredient, strength, dosage form and route of administration
   *
   *
   *                            Examples:
   *
   *
   *
   *
   *                               Pharmaceutical alternative: Erythromycin Ethylsuccinate for Erythromycin Stearate
   *
   *                               Pharmaceutical equivalent: Lisonpril for Zestril
   */
  EquivalentComposition: v3_substanceAdminSubstitution_EquivalentComposition,
  /**
   * Description: This substitution was performed or is permitted based on formulary guidelines.
   */
  Formulary: v3_substanceAdminSubstitution_Formulary,
  /**
   * Description: Substitution occurred or is permitted between equivalent Generics but not Brands
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Lisnopril (Lupin Corp) for Lisnopril (Wockhardt Corp)
   */
  GenericComposition: v3_substanceAdminSubstitution_GenericComposition,
  /**
   * No substitution occurred or is permitted.
   */
  None: v3_substanceAdminSubstitution_None,
  /**
   * Description: Substitution occurred or is permitted between therapeutically equivalent Brands but not Generics
   * >
   *                            Examples:
   *
   *
   *
   *                            Zantac for Tagamet
   */
  TherapeuticBrand: v3_substanceAdminSubstitution_TherapeuticBrand,
  /**
   * Description: Substitution occurred or is permitted with another product having the same therapeutic objective and safety profile.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            ranitidine for Tagamet
   */
  TherapeuticAlternative: v3_substanceAdminSubstitution_TherapeuticAlternative,
  /**
   * Description: Substitution occurred or is permitted between therapeutically equivalent Generics but not Brands
   * >
   *                            Examples:
   *
   *
   *
   *                            Ranitidine  for cimetidine
   */
  TherapeuticGeneric: v3_substanceAdminSubstitution_TherapeuticGeneric,
};
/**
 *   Description:
 * A task or action that a user may perform in a clinical information system.
 */
export const V3ActTaskCode = {
  /**
   * Description: A task or action that a user may perform in a clinical information system (e.g., medication order entry, laboratory test results review, problem list entry).
   */
  ActTaskCode: v3_ActCode_ActTaskCode,
  /**
   * Description: A person enters a known allergy for a given patient.
   */
  AllergyListEntry: v3_ActCode_AllergyListEntry,
  /**
   * Description: A person reviews a list of known allergies of a given patient.
   */
  AllergyListReview: v3_ActCode_AllergyListReview,
  /**
   * A person reviews a recommendation/assessment provided automatically by a clinical decision support application for a given patient.
   */
  ClinicalDecisionSupportInterventionReview: v3_ActCode_ClinicalDecisionSupportInterventionReview,
  /**
   * A clinician enters a clinical note about a given patient
   */
  ClinicalNoteEntryTask: v3_ActCode_ClinicalNoteEntryTask,
  /**
   * A person reviews a clinical note of a given patient.
   */
  ClinicalNoteReviewTask: v3_ActCode_ClinicalNoteReviewTask,
  /**
   * A clinician enters a diagnosis for a given patient.
   */
  DiagnosisListEntryTask: v3_ActCode_DiagnosisListEntryTask,
  /**
   * A person reviews a list of diagnoses of a given patient.
   */
  DiagnosisListReviewTask: v3_ActCode_DiagnosisListReviewTask,
  /**
   * A person provides a discharge instruction to a patient.
   */
  DischargeInstructionEntry: v3_ActCode_DischargeInstructionEntry,
  /**
   * A clinician enters a discharge summary for a given patient.
   */
  DischargeSummaryEntryTask: v3_ActCode_DischargeSummaryEntryTask,
  /**
   * A person reviews a discharge summary of a given patient.
   */
  DischargeSummaryReviewTask: v3_ActCode_DischargeSummaryReviewTask,
  /**
   * A person reviews a Falls Risk Assessment Instrument report of a given patient.
   */
  FallsRiskAssessmentInstrumentTask: v3_ActCode_FallsRiskAssessmentInstrumentTask,
  /**
   * Description: A person enters an immunization due or received for a given patient.
   */
  ImmunizationListEntry: v3_ActCode_ImmunizationListEntry,
  /**
   * Description: A person reviews a list of immunizations due or received for a given patient.
   */
  ImmunizationListReview: v3_ActCode_ImmunizationListReview,
  /**
   * A clinician creates a request for a laboratory test to be done for a given patient.
   */
  LaboratoryTestOrderEntryTask: v3_ActCode_LaboratoryTestOrderEntryTask,
  /**
   * A person reviews a list of laboratory results of a given patient.
   */
  LaboratoryResultsReviewTask: v3_ActCode_LaboratoryResultsReviewTask,
  /**
   * A clinician reviews a work list of medications to be administered to a given patient.
   */
  MedicationAdministrationRecordWorkListReviewTask: v3_ActCode_MedicationAdministrationRecordWorkListReviewTask,
  /**
   * A clinician creates a request for the administration of one or more medications to a given patient.
   */
  MedicationOrderEntryTask: v3_ActCode_MedicationOrderEntryTask,
  /**
   * A person reviews organisms of microbiology results of a given patient.
   */
  MicrobiologyOrganismsResultsReviewTask: v3_ActCode_MicrobiologyOrganismsResultsReviewTask,
  /**
   * A person reviews a list of microbiology results of a given patient.
   */
  MicrobiologyResultsReviewTask: v3_ActCode_MicrobiologyResultsReviewTask,
  /**
   * A person reviews the sensitivity test of microbiology results of a given patient.
   */
  MicrobiologySensitivityTestResultsReviewTask: v3_ActCode_MicrobiologySensitivityTestResultsReviewTask,
  /**
   * A person reviews a list of medication orders submitted to a given patient
   */
  MedicationListReviewTask: v3_ActCode_MedicationListReviewTask,
  /**
   * A clinician creates a request for a service to be performed for a given patient.
   */
  OrderEntryTask: v3_ActCode_OrderEntryTask,
  /**
   * A person reviews a list of orders submitted to a given patient.
   */
  OrdersReviewTask: v3_ActCode_OrdersReviewTask,
  /**
   * A person enters documentation about a given patient.
   */
  PatientDocumentationTask: v3_ActCode_PatientDocumentationTask,
  /**
   * A person provides a patient-specific education handout to a patient.
   */
  PatientEducationEntry: v3_ActCode_PatientEducationEntry,
  /**
   * A person (e.g., clinician, the patient herself) reviews patient information in the electronic medical record.
   */
  PatientInformationReviewTask: v3_ActCode_PatientInformationReviewTask,
  /**
   * A pathologist enters a report for a given patient.
   */
  PathologyReportEntryTask: v3_ActCode_PathologyReportEntryTask,
  /**
   * A person reviews a pathology report of a given patient.
   */
  PathologyReportReviewTask: v3_ActCode_PathologyReportReviewTask,
  /**
   * A clinician enters a problem for a given patient.
   */
  ProblemListEntryTask: v3_ActCode_ProblemListEntryTask,
  /**
   * A person reviews a list of problems of a given patient.
   */
  ProblemListReviewTask: v3_ActCode_ProblemListReviewTask,
  /**
   * A radiologist enters a report for a given patient.
   */
  RadiologyReportEntryTask: v3_ActCode_RadiologyReportEntryTask,
  /**
   * A person reviews a radiology report of a given patient.
   */
  RadiologyReportReviewTask: v3_ActCode_RadiologyReportReviewTask,
  /**
   * Description: A person enters a health care reminder for a given patient.
   */
  ReminderListEntry: v3_ActCode_ReminderListEntry,
  /**
   * Description: A person reviews a list of health care reminders for a given patient.
   */
  ReminderListReview: v3_ActCode_ReminderListReview,
  /**
   * A person reviews a Risk Assessment Instrument report of a given patient.
   */
  RiskAssessmentInstrumentTask: v3_ActCode_RiskAssessmentInstrumentTask,
  /**
   * Description: A person enters a wellness or preventive care reminder for a given patient.
   */
  WellnessReminderListEntry: v3_ActCode_WellnessReminderListEntry,
  /**
   * Description: A person reviews a list of wellness or preventive care reminders for a given patient.
   */
  WellnessReminderListReview: v3_ActCode_WellnessReminderListReview,
};
const v3_ActUncertainty_StatedWithNoAssertionOfUncertainty: Coding = {
  code: 'N',
  display: 'stated with no assertion of uncertainty',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActUncertainty',
};
const v3_ActUncertainty_StatedWithUncertainty: Coding = {
  code: 'U',
  display: 'stated with uncertainty',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActUncertainty',
};
/**
 *   OpenIssue:
 * Missing Description
 */
export const V3ActUncertainty = {
  /**
   * Specifies that the act statement is made without explicit tagging of uncertainty. This is the normal statement, meaning that it is not free of errors and uncertainty may still exist.
   */
  StatedWithNoAssertionOfUncertainty: v3_ActUncertainty_StatedWithNoAssertionOfUncertainty,
  /**
   * Specifies that the originator of the Act statement does not have full confidence in the applicability (i.e., in event mood: factual truth) of the statement.
   */
  StatedWithUncertainty: v3_ActUncertainty_StatedWithUncertainty,
};
const v3_AddressPartType_AdditionalLocator: Coding = {
  code: 'ADL',
  display: 'additional locator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_AddressLine: Coding = {
  code: 'AL',
  display: 'address line',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_BuildingNumberNumeric: Coding = {
  code: 'BNN',
  display: 'building number numeric',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_BuildingNumber: Coding = {
  code: 'BNR',
  display: 'building number',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_BuildingNumberSuffix: Coding = {
  code: 'BNS',
  display: 'building number suffix',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_CareOf: Coding = {
  code: 'CAR',
  display: 'care of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_CensusTract: Coding = {
  code: 'CEN',
  display: 'census tract',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_Country: Coding = {
  code: 'CNT',
  display: 'country',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_CountyOrParish: Coding = {
  code: 'CPA',
  display: 'county or parish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_Municipality: Coding = {
  code: 'CTY',
  display: 'municipality',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_DeliveryAddressLine: Coding = {
  code: 'DAL',
  display: 'delivery address line',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_Delimiter: Coding = {
  code: 'DEL',
  display: 'delimiter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_DeliveryInstallationType: Coding = {
  code: 'DINST',
  display: 'delivery installation type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_DeliveryInstallationArea: Coding = {
  code: 'DINSTA',
  display: 'delivery installation area',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_DeliveryInstallationQualifier: Coding = {
  code: 'DINSTQ',
  display: 'delivery installation qualifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_Direction: Coding = {
  code: 'DIR',
  display: 'direction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_DeliveryMode: Coding = {
  code: 'DMOD',
  display: 'delivery mode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_DeliveryModeIdentifier: Coding = {
  code: 'DMODID',
  display: 'delivery mode identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_DeliveryPointIdentifier: Coding = {
  code: 'DPID',
  display: 'delivery point identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_Intersection: Coding = {
  code: 'INT',
  display: 'intersection',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_PostBox: Coding = {
  code: 'POB',
  display: 'post box',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_Precinct: Coding = {
  code: 'PRE',
  display: 'precinct',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_StreetAddressLine: Coding = {
  code: 'SAL',
  display: 'street address line',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_StateOrProvince: Coding = {
  code: 'STA',
  display: 'state or province',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_StreetNameBase: Coding = {
  code: 'STB',
  display: 'street name base',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_StreetName: Coding = {
  code: 'STR',
  display: 'street name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_StreetType: Coding = {
  code: 'STTYP',
  display: 'street type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_UnitIdentifier: Coding = {
  code: 'UNID',
  display: 'unit identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_UnitDesignator: Coding = {
  code: 'UNIT',
  display: 'unit designator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
const v3_AddressPartType_PostalCode: Coding = {
  code: 'ZIP',
  display: 'postal code',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressPartType',
};
/**
 *   Description:
 * Code that specifies whether an address part names the street, city, country, postal code, post box, etc. Discussion: The hierarchical nature of these concepts shows composition.  E.g. "Street Name" is part of "Street Address Line"
 */
export const V3AddressPartType = {
  /**
   * This can be a unit designator, such as apartment number, suite number, or floor. There may be several unit designators in an address (e.g., "3rd floor, Appt. 342"). This can also be a designator pointing away from the location, rather than specifying a smaller location within some larger one (e.g., Dutch "t.o." means "opposite to" for house boats located across the street facing houses).
   */
  AdditionalLocator: v3_AddressPartType_AdditionalLocator,
  /**
   * Description: An address line is for either an additional locator, a delivery address or a street address.
   */
  AddressLine: v3_AddressPartType_AddressLine,
  /**
   * The numeric portion of a building number
   */
  BuildingNumberNumeric: v3_AddressPartType_BuildingNumberNumeric,
  /**
   * The number of a building, house or lot alongside the street.  Also known as "primary street number".  This does not number the street but rather the building.
   */
  BuildingNumber: v3_AddressPartType_BuildingNumber,
  /**
   * Any alphabetic character, fraction or other text that may appear after the numeric portion of a building number
   */
  BuildingNumberSuffix: v3_AddressPartType_BuildingNumberSuffix,
  /**
   * The name of the party who will take receipt at the specified address, and will take on responsibility for ensuring delivery to the target recipient
   */
  CareOf: v3_AddressPartType_CareOf,
  /**
   * A geographic sub-unit delineated for demographic purposes.
   */
  CensusTract: v3_AddressPartType_CensusTract,
  /**
   * Country
   */
  Country: v3_AddressPartType_Country,
  /**
   * A sub-unit of a state or province. (49 of the United States of America use the term "county;" Louisiana uses the term "parish".)
   */
  CountyOrParish: v3_AddressPartType_CountyOrParish,
  /**
   * The name of the city, town, village, or other community or delivery center
   */
  Municipality: v3_AddressPartType_Municipality,
  /**
   * A delivery address line is frequently used instead of breaking out delivery mode, delivery installation, etc.  An address generally has only a delivery address line or a street address line, but not both.
   */
  DeliveryAddressLine: v3_AddressPartType_DeliveryAddressLine,
  /**
   * Delimiters are printed without framing white space.  If no value component is provided, the delimiter appears as a line break.
   */
  Delimiter: v3_AddressPartType_Delimiter,
  /**
   * Indicates the type of delivery installation (the facility to which the mail will be delivered prior to final shipping via the delivery mode.) Example: post office, letter carrier depot, community mail center, station, etc.
   */
  DeliveryInstallationType: v3_AddressPartType_DeliveryInstallationType,
  /**
   * The location of the delivery installation, usually a town or city, and is only required if the area is different from the municipality. Area to which mail delivery service is provided from any postal facility or service such as an individual letter carrier, rural route, or postal route.
   */
  DeliveryInstallationArea: v3_AddressPartType_DeliveryInstallationArea,
  /**
   * A number, letter or name identifying a delivery installation.  E.g., for Station A, the delivery installation qualifier would be 'A'.
   */
  DeliveryInstallationQualifier: v3_AddressPartType_DeliveryInstallationQualifier,
  /**
   * Direction (e.g., N, S, W, E)
   */
  Direction: v3_AddressPartType_Direction,
  /**
   * Indicates the type of service offered, method of delivery.  For example: post office box, rural route, general delivery, etc.
   */
  DeliveryMode: v3_AddressPartType_DeliveryMode,
  /**
   * Represents the routing information such as a letter carrier route number.  It is the identifying number of the designator (the box number or rural route number).
   */
  DeliveryModeIdentifier: v3_AddressPartType_DeliveryModeIdentifier,
  /**
   * A value that uniquely identifies the postal address.
   */
  DeliveryPointIdentifier: v3_AddressPartType_DeliveryPointIdentifier,
  /**
   * Description:An intersection denotes that the actual address is located AT or CLOSE TO the intersection OF two or more streets.
   */
  Intersection: v3_AddressPartType_Intersection,
  /**
   * A numbered box located in a post station.
   */
  PostBox: v3_AddressPartType_PostBox,
  /**
   * A subsection of a municipality
   */
  Precinct: v3_AddressPartType_Precinct,
  /**
   * Description: A street address line is frequently used instead of breaking out build number, street name, street type, etc. An address generally has only a delivery address line or a street address line, but not both.
   */
  StreetAddressLine: v3_AddressPartType_StreetAddressLine,
  /**
   * A sub-unit of a country with limited sovereignty in a federally organized country.
   */
  StateOrProvince: v3_AddressPartType_StateOrProvince,
  /**
   * The base name of a roadway or artery recognized by a municipality (excluding street type and direction)
   */
  StreetNameBase: v3_AddressPartType_StreetNameBase,
  /**
   * street name
   */
  StreetName: v3_AddressPartType_StreetName,
  /**
   * The designation given to the street.  (e.g. Street, Avenue, Crescent, etc.)
   */
  StreetType: v3_AddressPartType_StreetType,
  /**
   * The number or name of a specific unit contained within a building or complex, as assigned by that building or complex.
   */
  UnitIdentifier: v3_AddressPartType_UnitIdentifier,
  /**
   * Indicates the type of specific unit contained within a building or complex.  E.g. Appartment, Floor
   */
  UnitDesignator: v3_AddressPartType_UnitDesignator,
  /**
   * A postal code designating a region defined by the postal service.
   */
  PostalCode: v3_AddressPartType_PostalCode,
};
const v3_AddressUse_GeneralAddressUse: Coding = {
  code: '_GeneralAddressUse',
  display: '_GeneralAddressUse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_PostalAddressUse: Coding = {
  code: '_PostalAddressUse',
  display: '_PostalAddressUse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_TelecommunicationAddressUse: Coding = {
  code: '_TelecommunicationAddressUse',
  display: '_TelecommunicationAddressUse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_AnsweringService: Coding = {
  code: 'AS',
  display: 'answering service',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_EmergencyContact: Coding = {
  code: 'EC',
  display: 'emergency contact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_HomeAddress: Coding = {
  code: 'H',
  display: 'home address',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_MobileContact: Coding = {
  code: 'MC',
  display: 'mobile contact)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_NoLongerInUse: Coding = {
  code: 'OLD',
  display: 'no longer in use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_Pager: Coding = {
  code: 'PG',
  display: 'pager',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_TemporaryAddress: Coding = {
  code: 'TMP',
  display: 'temporary address',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
const v3_AddressUse_WorkPlace: Coding = {
  code: 'WP',
  display: 'work place',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AddressUse',
};
/**
 *  Codes that provide guidance around the circumstances in which a given address should be used.
 */
export const V3AddressUse = {
  /**
   * Description: Address uses that can apply to both postal and telecommunication addresses.
   */
  GeneralAddressUse: v3_AddressUse_GeneralAddressUse,
  /**
   * Description: Address uses that only apply to postal addresses, not telecommunication addresses.
   */
  PostalAddressUse: v3_AddressUse_PostalAddressUse,
  /**
   * Description: Address uses that only apply to telecommunication addresses, not postal addresses.
   */
  TelecommunicationAddressUse: v3_AddressUse_TelecommunicationAddressUse,
  /**
   * Description: An automated answering machine used for less urgent cases and if the main purpose of contact is to leave a message or access an automated announcement.
   */
  AnsweringService: v3_AddressUse_AnsweringService,
  /**
   * Description: A flag indicating that the address is bad, in fact, useless.
   */
  BadAddress: v3_AddressUse_BadAddress,
  /**
   * Description: Indicates that the address is considered sensitive and should only be shared or published in accordance with organizational controls governing patient demographic information with increased sensitivity. Uses of Addresses.   Lloyd to supply more complete description.
   */
  ConfidentialAddress: v3_AddressUse_ConfidentialAddress,
  /**
   * Description: Indicates a work place address or telecommunication address that reaches the individual or organization directly without intermediaries. For phones, often referred to as a 'private line'.
   */
  Direct: v3_AddressUse_Direct,
  /**
   * Description: A contact specifically designated to be used for emergencies. This is the first choice in emergencies, independent of any other use codes.
   */
  EmergencyContact: v3_AddressUse_EmergencyContact,
  /**
   * Description: A communication address at a home, attempted contacts for business purposes might intrude privacy and chances are one will contact family or other household members instead of the person one wishes to call. Typically used with urgent cases, or if no other contacts are available.
   */
  HomeAddress: v3_AddressUse_HomeAddress,
  /**
   * Description: The primary home, to reach a person after business hours.
   */
  PrimaryHome: v3_AddressUse_PrimaryHome,
  /**
   * Description: A vacation home, to reach a person while on vacation.
   */
  VacationHome: v3_AddressUse_VacationHome,
  /**
   * Description: A telecommunication device that moves and stays with its owner. May have characteristics of all other use codes, suitable for urgent matters, not the first choice for routine business.
   */
  MobileContact: v3_AddressUse_MobileContact,
  /**
   * This address is no longer in use.
   *
   *
   *                            Usage Note: Address may also carry valid time ranges. This code is used to cover the situations where it is known that the address is no longer valid, but no particular time range for its use is known.
   */
  NoLongerInUse: v3_AddressUse_NoLongerInUse,
  /**
   * Description: A paging device suitable to solicit a callback or to leave a very short message.
   */
  Pager: v3_AddressUse_Pager,
  /**
   * Description: Used primarily to visit an address.
   */
  PhysicalVisitAddress: v3_AddressUse_PhysicalVisitAddress,
  /**
   * Description: Used to send mail.
   */
  PostalAddress: v3_AddressUse_PostalAddress,
  /**
   * Description: Indicates a work place address or telecommunication address that is a 'standard' address which may reach a reception service, mail-room, or other intermediary prior to the target entity.
   */
  Public: v3_AddressUse_Public,
  /**
   * Description: A temporary address, may be good for visit or mailing. Note that an address history can provide more detailed information.
   */
  TemporaryAddress: v3_AddressUse_TemporaryAddress,
  /**
   * Description: An office address. First choice for business related contacts during business hours.
   */
  WorkPlace: v3_AddressUse_WorkPlace,
};
const v3_AdministrativeGender_Female: Coding = {
  code: 'F',
  display: 'Female',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AdministrativeGender',
};
const v3_AdministrativeGender_Male: Coding = {
  code: 'M',
  display: 'Male',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AdministrativeGender',
};
const v3_AdministrativeGender_Undifferentiated: Coding = {
  code: 'UN',
  display: 'Undifferentiated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AdministrativeGender',
};
/**
 *  The gender of a person used for adminstrative purposes (as opposed to clinical gender)
 */
export const V3AdministrativeGender = {
  /**
   * Female
   */
  Female: v3_AdministrativeGender_Female,
  /**
   * Male
   */
  Male: v3_AdministrativeGender_Male,
  /**
   * The gender of a person could not be uniquely defined as male or female, such as hermaphrodite.
   */
  Undifferentiated: v3_AdministrativeGender_Undifferentiated,
};
const v3_AmericanIndianAlaskaNativeLanguages_Abenakian: Coding = {
  code: '_Abenakian',
  display: 'Abenakian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Aleut: Coding = {
  code: '_Aleut',
  display: 'Aleut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Algic: Coding = {
  code: '_Algic',
  display: 'Algic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Algonquian: Coding = {
  code: '_Algonquian',
  display: 'Algonquian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Apachean: Coding = {
  code: '_Apachean',
  display: 'Apachean',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Arapahoan: Coding = {
  code: '_Arapahoan',
  display: 'Arapahoan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_ArapahoGrosVentre: Coding = {
  code: '_ArapahoGrosVentre',
  display: 'ArapahoGrosVentre',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Athapaskan: Coding = {
  code: '_Athapaskan',
  display: 'Athapaskan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_AthapaskanEyak: Coding = {
  code: '_AthapaskanEyak',
  display: 'AthapaskanEyak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Caddoan: Coding = {
  code: '_Caddoan',
  display: 'Caddoan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Cahitan: Coding = {
  code: '_Cahitan',
  display: 'Cahitan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CaliforniaAthapaskan: Coding = {
  code: '_CaliforniaAthapaskan',
  display: 'CaliforniaAthapaskan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CentralAlaskaYukon: Coding = {
  code: '_CentralAlaskaYukon',
  display: 'CentralAlaskaYukon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CentralMuskogean: Coding = {
  code: '_CentralMuskogean',
  display: 'CentralMuskogean',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CentralNumic: Coding = {
  code: '_CentralNumic',
  display: 'CentralNumic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CentralSalish: Coding = {
  code: '_CentralSalish',
  display: 'CentralSalish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Chimakuan: Coding = {
  code: '_Chimakuan',
  display: 'Chimakuan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Chinookan: Coding = {
  code: '_Chinookan',
  display: 'Chinookan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_ChiwereWinnebago: Coding = {
  code: '_ChiwereWinnebago',
  display: 'ChiwereWinnebago',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CochimiYuman: Coding = {
  code: '_CochimiYuman',
  display: 'CochimiYuman',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Coosan: Coding = {
  code: '_Coosan',
  display: 'Coosan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Cree: Coding = {
  code: '_Cree',
  display: 'Cree',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CreeMontagnais: Coding = {
  code: '_CreeMontagnais',
  display: 'CreeMontagnais',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Cupan: Coding = {
  code: '_Cupan',
  display: 'Cupan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Dakotan: Coding = {
  code: '_Dakotan',
  display: 'Dakotan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Delawaran: Coding = {
  code: '_Delawaran',
  display: 'Delawaran',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_DeltaCalifornia: Coding = {
  code: '_DeltaCalifornia',
  display: 'DeltaCalifornia',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Dhegiha: Coding = {
  code: '_Dhegiha',
  display: 'Dhegiha',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Diegueno: Coding = {
  code: '_Diegueno',
  display: 'Diegueno',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_EasternAlgonquin: Coding = {
  code: '_EasternAlgonquin',
  display: 'EasternAlgonquin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_EasternApachean: Coding = {
  code: '_EasternApachean',
  display: 'EasternApachean',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_EasternMiwok: Coding = {
  code: '_EasternMiwok',
  display: 'EasternMiwok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_EskimoAleut: Coding = {
  code: '_EskimoAleut',
  display: 'EskimoAleut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Eskimoan: Coding = {
  code: '_Eskimoan',
  display: 'Eskimoan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Hokan: Coding = {
  code: '_Hokan',
  display: 'Hokan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_InteriorSalish: Coding = {
  code: '_InteriorSalish',
  display: 'InteriorSalish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_InuitInupiaq: Coding = {
  code: '_InuitInupiaq',
  display: 'InuitInupiaq',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Iroquoian: Coding = {
  code: '_Iroquoian',
  display: 'Iroquoian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Kalapuyan: Coding = {
  code: '_Kalapuyan',
  display: 'Kalapuyan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Keresan: Coding = {
  code: '_Keresan',
  display: 'Keresan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_KiowaTanoan: Coding = {
  code: '_KiowaTanoan',
  display: 'KiowaTanoan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_KoyukonIngalik: Coding = {
  code: '_KoyukonIngalik',
  display: 'KoyukonIngalik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_KutchinHan: Coding = {
  code: '_KutchinHan',
  display: 'KutchinHan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Maiduan: Coding = {
  code: '_Maiduan',
  display: 'Maiduan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_MississippiValley: Coding = {
  code: '_MississippiValley',
  display: 'MississippiValley',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_MissouriRiver: Coding = {
  code: '_MissouriRiver',
  display: 'MissouriRiver',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Miwokan: Coding = {
  code: '_Miwokan',
  display: 'Miwokan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Muskogean: Coding = {
  code: '_Muskogean',
  display: 'Muskogean',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Nadene: Coding = {
  code: '_Nadene',
  display: 'Nadene',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Nootkan: Coding = {
  code: '_Nootkan',
  display: 'Nootkan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_NorthernCaddoan: Coding = {
  code: '_NorthernCaddoan',
  display: 'NorthernCaddoan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_NorthernIroquoian: Coding = {
  code: '_NorthernIroquoian',
  display: 'NorthernIroquoian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Numic: Coding = {
  code: '_Numic',
  display: 'Numic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Ojibwayan: Coding = {
  code: '_Ojibwayan',
  display: 'Ojibwayan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_OregonAthapaskan: Coding = {
  code: '_OregonAthapaskan',
  display: 'OregonAthapaskan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_PacificCoastAthapaskan: Coding = {
  code: '_PacificCoastAthapaskan',
  display: 'PacificCoastAthapaskan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Pai: Coding = {
  code: '_Pai',
  display: 'Pai',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Palaihnihan: Coding = {
  code: '_Palaihnihan',
  display: 'Palaihnihan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Penutian: Coding = {
  code: '_Penutian',
  display: 'Penutian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Pidgin: Coding = {
  code: '_Pidgin',
  display: 'Pidgin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_PlateauPenutian: Coding = {
  code: '_PlateauPenutian',
  display: 'PlateauPenutian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Pomoan: Coding = {
  code: '_Pomoan',
  display: 'Pomoan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Ritwan: Coding = {
  code: '_Ritwan',
  display: 'Ritwan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_River: Coding = {
  code: '_River',
  display: 'River',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Sahaptian: Coding = {
  code: '_Sahaptian',
  display: 'Sahaptian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Salishan: Coding = {
  code: '_Salishan',
  display: 'Salishan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_SaukFoxKickapoo: Coding = {
  code: '_SaukFoxKickapoo',
  display: 'SaukFoxKickapoo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_SerranoGabrielino: Coding = {
  code: '_SerranoGabrielino',
  display: 'SerranoGabrielino',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Shasta: Coding = {
  code: '_Shasta',
  display: 'Shasta',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Siouan: Coding = {
  code: '_Siouan',
  display: 'Siouan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_SiouanCatawba: Coding = {
  code: '_SiouanCatawba',
  display: 'SiouanCatawba',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_SirenikskiYupik: Coding = {
  code: '_SirenikskiYupik',
  display: 'SirenikskiYupik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_SouthernAlaska: Coding = {
  code: '_SouthernAlaska',
  display: 'SouthernAlaska',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_SouthernCaddoan: Coding = {
  code: '_SouthernCaddoan',
  display: 'SouthernCaddoan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_SouthernNumic: Coding = {
  code: '_SouthernNumic',
  display: 'SouthernNumic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Takelman: Coding = {
  code: '_Takelman',
  display: 'Takelman',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Takic: Coding = {
  code: '_Takic',
  display: 'Takic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tanana: Coding = {
  code: '_Tanana',
  display: 'Tanana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_TananaTutchone: Coding = {
  code: '_TananaTutchone',
  display: 'TananaTutchone',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Taracahitan: Coding = {
  code: '_Taracahitan',
  display: 'Taracahitan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tepiman: Coding = {
  code: '_Tepiman',
  display: 'Tepiman',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tiwa: Coding = {
  code: '_Tiwa',
  display: 'Tiwa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tsamosan: Coding = {
  code: '_Tsamosan',
  display: 'Tsamosan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tsimshianic: Coding = {
  code: '_Tsimshianic',
  display: 'Tsimshianic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_UpperChinook: Coding = {
  code: '_UpperChinook',
  display: 'UpperChinook',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Utian: Coding = {
  code: '_Utian',
  display: 'Utian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_UtoAztecan: Coding = {
  code: '_UtoAztecan',
  display: 'UtoAztecan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Wakashan: Coding = {
  code: '_Wakashan',
  display: 'Wakashan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_WesternApachean: Coding = {
  code: '_WesternApachean',
  display: 'WesternApachean',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_WesternMiwok: Coding = {
  code: '_WesternMiwok',
  display: 'WesternMiwok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_WesternMuskogean: Coding = {
  code: '_WesternMuskogean',
  display: 'WesternMuskogean',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_WesternNumic: Coding = {
  code: '_WesternNumic',
  display: 'WesternNumic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Wintuan: Coding = {
  code: '_Wintuan',
  display: 'Wintuan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Yokutsan: Coding = {
  code: '_Yokutsan',
  display: 'Yokutsan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Yukian: Coding = {
  code: '_Yukian',
  display: 'Yukian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Yuman: Coding = {
  code: '_Yuman',
  display: 'Yuman',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_EasternAbenaki: Coding = {
  code: 'x-AAQ',
  display: 'Eastern Abenaki',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_WesternAbenaki: Coding = {
  code: 'x-ABE',
  display: 'Western Abenaki',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Achumawi: Coding = {
  code: 'x-ACH',
  display: 'Achumawi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Ahtna: Coding = {
  code: 'x-AHT',
  display: 'Ahtna',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Alabama: Coding = {
  code: 'x-AKZ',
  display: 'Alabama',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Aleut_2: Coding = {
  code: 'x-ALW',
  display: 'Aleut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Jicarilla: Coding = {
  code: 'x-APJ',
  display: 'Jicarilla',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_KiowaApache: Coding = {
  code: 'x-APK',
  display: 'Kiowa Apache',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Lipan: Coding = {
  code: 'x-APL',
  display: 'Lipan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_MescaleroChiricahua: Coding = {
  code: 'x-APM',
  display: 'Mescalero-Chiricahua',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_WesternApache: Coding = {
  code: 'x-APW',
  display: 'Western Apache',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Arikara: Coding = {
  code: 'x-ARI',
  display: 'Arikara',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Arapaho: Coding = {
  code: 'x-ARP',
  display: 'Arapaho',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Assiniboine: Coding = {
  code: 'x-ASB',
  display: 'Assiniboine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_GrosVentre: Coding = {
  code: 'x-ATS',
  display: 'Gros Ventre',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Atsugewi: Coding = {
  code: 'x-ATW',
  display: 'Atsugewi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Blackfoot: Coding = {
  code: 'x-BLC',
  display: 'Blackfoot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Caddo: Coding = {
  code: 'x-CAD',
  display: 'Caddo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Cayuga: Coding = {
  code: 'x-CAY',
  display: 'Cayuga',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Choctaw: Coding = {
  code: 'x-CCT',
  display: 'Choctaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_LowerChehalis: Coding = {
  code: 'x-CEA',
  display: 'Lower Chehalis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Cherokee: Coding = {
  code: 'x-CER',
  display: 'Cherokee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_ChinookWawa: Coding = {
  code: 'x-CHH',
  display: 'Chinook Wawa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Cahuilla: Coding = {
  code: 'x-CHL',
  display: 'Cahuilla',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Cheyenne: Coding = {
  code: 'x-CHY',
  display: 'Cheyenne',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Chickasaw: Coding = {
  code: 'x-CIC',
  display: 'Chickasaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_UpperChehalis: Coding = {
  code: 'x-CJH',
  display: 'Upper Chehalis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Koasati: Coding = {
  code: 'x-CKU',
  display: 'Koasati',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Clallam: Coding = {
  code: 'x-CLM',
  display: 'Clallam',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Cocopa: Coding = {
  code: 'x-COC',
  display: 'Cocopa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Columbian: Coding = {
  code: 'x-COL',
  display: 'Columbian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Comanche: Coding = {
  code: 'x-COM',
  display: 'Comanche',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Hanis: Coding = {
  code: 'x-COS',
  display: 'Hanis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Cowlitz: Coding = {
  code: 'x-COW',
  display: 'Cowlitz',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CoeurDQuoteAlene: Coding = {
  code: 'x-CRD',
  display: "Coeur D'alene",
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Creek: Coding = {
  code: 'x-CRK',
  display: 'Creek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Crow: Coding = {
  code: 'x-CRO',
  display: 'Crow',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_WesternCree: Coding = {
  code: 'x-CRP',
  display: 'Western Cree',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CoastMiwok: Coding = {
  code: 'x-CSI',
  display: 'Coast Miwok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CentralSierraMiwok: Coding = {
  code: 'x-CSM',
  display: 'Central Sierra Miwok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Cupeno: Coding = {
  code: 'x-CUP',
  display: 'Cupeno',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_UnamiDelaware: Coding = {
  code: 'x-DEL',
  display: 'Unami Delaware',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Dakota: Coding = {
  code: 'x-DHG',
  display: 'Dakota',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Kumeyaay: Coding = {
  code: 'x-DIH',
  display: 'Kumeyaay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_PacificYupikGulf: Coding = {
  code: 'x-EMS',
  display: 'Pacific Yupik Gulf',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_KingsRiver: Coding = {
  code: 'x-ENH',
  display: 'Kings River',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_NorthAlaskanInuktitut: Coding = {
  code: 'x-ESI',
  display: 'North Alaskan Inuktitut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_NorthwestAlaskaInuktitut: Coding = {
  code: 'x-ESK',
  display: 'Northwest Alaska Inuktitut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CentralSiberianYupik: Coding = {
  code: 'x-ESS',
  display: 'Central Siberian Yupik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CentralAlaskanYupik: Coding = {
  code: 'x-ESU',
  display: 'Central Alaskan Yupik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Eyak: Coding = {
  code: 'x-EYA',
  display: 'Eyak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Kalispel: Coding = {
  code: 'x-FLA',
  display: 'Kalispel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Gashowu: Coding = {
  code: 'x-GSH',
  display: 'Gashowu',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Han: Coding = {
  code: 'x-HAA',
  display: 'Han',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Haida: Coding = {
  code: 'x-HAI',
  display: 'Haida',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Hidatsa: Coding = {
  code: 'x-HID',
  display: 'Hidatsa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Holikachuk: Coding = {
  code: 'x-HOI',
  display: 'Holikachuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Hopi: Coding = {
  code: 'x-HOP',
  display: 'Hopi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Hupa: Coding = {
  code: 'x-HUP',
  display: 'Hupa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_DegexitQuoteAn: Coding = {
  code: 'x-ING',
  display: "Degexit'an",
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Chiwere: Coding = {
  code: 'x-IOW',
  display: 'Chiwere',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Kansa: Coding = {
  code: 'x-KAA',
  display: 'Kansa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CentralKalapuyan: Coding = {
  code: 'x-KAL',
  display: 'Central Kalapuyan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Kawaiisu: Coding = {
  code: 'x-KAW',
  display: 'Kawaiisu',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_RioGrandeKeresan: Coding = {
  code: 'x-KEE',
  display: 'Rio Grande Keresan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Kickapoo: Coding = {
  code: 'x-KIC',
  display: 'Kickapoo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Kiowa: Coding = {
  code: 'x-KIO',
  display: 'Kiowa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_AcomaLaguna: Coding = {
  code: 'x-KJQ',
  display: 'Acoma-Laguna',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Kashaya: Coding = {
  code: 'x-KJU',
  display: 'Kashaya',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_KlamathModoc: Coding = {
  code: 'x-KLA',
  display: 'Klamath-Modoc',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Koyukon: Coding = {
  code: 'x-KOY',
  display: 'Koyukon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Cahto: Coding = {
  code: 'x-KTW',
  display: 'Cahto',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Kutchin: Coding = {
  code: 'x-KUC',
  display: 'Kutchin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Kootenai: Coding = {
  code: 'x-KUN',
  display: 'Kootenai',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_UpperKuskokwim: Coding = {
  code: 'x-KUU',
  display: 'Upper Kuskokwim',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Karok: Coding = {
  code: 'x-KYH',
  display: 'Karok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Lakota: Coding = {
  code: 'x-LKT',
  display: 'Lakota',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_LakeMiwok: Coding = {
  code: 'x-LMW',
  display: 'Lake Miwok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Luiseno: Coding = {
  code: 'x-LUI',
  display: 'Luiseno',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Lushootseed: Coding = {
  code: 'x-LUT',
  display: 'Lushootseed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_MaliseetPassamaquoddy: Coding = {
  code: 'x-MAC',
  display: 'Maliseet-Passamaquoddy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_NorthwestMaidu: Coding = {
  code: 'x-MAI',
  display: 'Northwest Maidu',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Menominee: Coding = {
  code: 'x-MEZ',
  display: 'Menominee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Mandan: Coding = {
  code: 'x-MHQ',
  display: 'Mandan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Micmac: Coding = {
  code: 'x-MIC',
  display: 'Micmac',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Mikasuki: Coding = {
  code: 'x-MIK',
  display: 'Mikasuki',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Mohawk: Coding = {
  code: 'x-MOH',
  display: 'Mohawk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Mono: Coding = {
  code: 'x-MON',
  display: 'Mono',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Mohave: Coding = {
  code: 'x-MOV',
  display: 'Mohave',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Maricopa: Coding = {
  code: 'x-MRC',
  display: 'Maricopa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Makah: Coding = {
  code: 'x-MYH',
  display: 'Makah',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Dine: Coding = {
  code: 'x-NAV',
  display: 'Dine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_NezPerce: Coding = {
  code: 'x-NEZ',
  display: 'Nez Perce',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Nakota: Coding = {
  code: 'x-NKT',
  display: 'Nakota',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_NortheastMaidu: Coding = {
  code: 'x-NMU',
  display: 'Northeast Maidu',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_NorthernSierraMiwok: Coding = {
  code: 'x-NSQ',
  display: 'Northern Sierra Miwok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Nisenan: Coding = {
  code: 'x-NSZ',
  display: 'Nisenan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_SouthernOjibwa: Coding = {
  code: 'x-OJB',
  display: 'Southern Ojibwa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Okanagan: Coding = {
  code: 'x-OKA',
  display: 'Okanagan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_OmahaPonca: Coding = {
  code: 'x-OMA',
  display: 'Omaha-Ponca',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Oneida: Coding = {
  code: 'x-ONE',
  display: 'Oneida',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Onondaga: Coding = {
  code: 'x-ONO',
  display: 'Onondaga',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Osage: Coding = {
  code: 'x-OSA',
  display: 'Osage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_NorthernPaiuteBannock: Coding = {
  code: 'x-PAO',
  display: 'Northern Paiute-Bannock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_PapagoPima: Coding = {
  code: 'x-PAP',
  display: 'Papago-Pima',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Panamint: Coding = {
  code: 'x-PAR',
  display: 'Panamint',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Pawnee: Coding = {
  code: 'x-PAW',
  display: 'Pawnee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_NortheasternPomo: Coding = {
  code: 'x-PEF',
  display: 'Northeastern Pomo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_SoutheasternPomo: Coding = {
  code: 'x-PEO',
  display: 'Southeastern Pomo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_SouthernPomo: Coding = {
  code: 'x-PEQ',
  display: 'Southern Pomo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_PlainsMiwok: Coding = {
  code: 'x-PMW',
  display: 'Plains Miwok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CentralPomo: Coding = {
  code: 'x-POO',
  display: 'Central Pomo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Potawatami: Coding = {
  code: 'x-POT',
  display: 'Potawatami',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_PlainsIndianSignLanguage: Coding = {
  code: 'x-PSD',
  display: 'Plains Indian Sign Language',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_PosoCreek: Coding = {
  code: 'x-PYL',
  display: 'Poso Creek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Quapaw: Coding = {
  code: 'x-QUA',
  display: 'Quapaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Quileute: Coding = {
  code: 'x-QUI',
  display: 'Quileute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Quinault: Coding = {
  code: 'x-QUN',
  display: 'Quinault',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Mesquakie: Coding = {
  code: 'x-SAC',
  display: 'Mesquakie',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Seneca: Coding = {
  code: 'x-SEE',
  display: 'Seneca',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Serrano: Coding = {
  code: 'x-SER',
  display: 'Serrano',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Shoshone: Coding = {
  code: 'x-SHH',
  display: 'Shoshone',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Shasta_2: Coding = {
  code: 'x-SHT',
  display: 'Shasta',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Shawnee: Coding = {
  code: 'x-SJW',
  display: 'Shawnee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_SouthernSierraMiwok: Coding = {
  code: 'x-SKD',
  display: 'Southern Sierra Miwok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_NorthernStraits: Coding = {
  code: 'x-STR',
  display: 'Northern Straits',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_LowerTanana: Coding = {
  code: 'x-TAA',
  display: 'Lower Tanana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_NorthernTiwa: Coding = {
  code: 'x-TAO',
  display: 'Northern Tiwa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_UpperTanana: Coding = {
  code: 'x-TAU',
  display: 'Upper Tanana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tanacross: Coding = {
  code: 'x-TCB',
  display: 'Tanacross',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tewa: Coding = {
  code: 'x-TEW',
  display: 'Tewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tanaina: Coding = {
  code: 'x-TFN',
  display: 'Tanaina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_SouthernTiwa: Coding = {
  code: 'x-TIX',
  display: 'Southern Tiwa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_TuleKaweah: Coding = {
  code: 'x-TKH',
  display: 'Tule-Kaweah',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tlingit: Coding = {
  code: 'x-TLI',
  display: 'Tlingit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tolowa: Coding = {
  code: 'x-TOL',
  display: 'Tolowa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Jemez: Coding = {
  code: 'x-TOW',
  display: 'Jemez',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_CoastTsimshain: Coding = {
  code: 'x-TSI',
  display: 'Coast Tsimshain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tubatululabal: Coding = {
  code: 'x-TUB',
  display: 'Tubatululabal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tuscarora: Coding = {
  code: 'x-TUS',
  display: 'Tuscarora',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tututni: Coding = {
  code: 'x-TUU',
  display: 'Tututni',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Umatilla: Coding = {
  code: 'x-UMA',
  display: 'Umatilla',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_UteSouthernPaiute: Coding = {
  code: 'x-UTE',
  display: 'Ute-Southern Paiute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_WallaWalla: Coding = {
  code: 'x-WAA',
  display: 'Walla Walla',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Kiksht: Coding = {
  code: 'x-WAC',
  display: 'Kiksht',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Wappo: Coding = {
  code: 'x-WAO',
  display: 'Wappo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Tenino: Coding = {
  code: 'x-WAR',
  display: 'Tenino',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Washoe: Coding = {
  code: 'x-WAS',
  display: 'Washoe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Wichita: Coding = {
  code: 'x-WIC',
  display: 'Wichita',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Hocak: Coding = {
  code: 'x-WIN',
  display: 'Hocak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_WintuNomlaki: Coding = {
  code: 'x-WIT',
  display: 'Wintu-Nomlaki',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Yakima: Coding = {
  code: 'x-YAK',
  display: 'Yakima',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Yaqui: Coding = {
  code: 'x-YAQ',
  display: 'Yaqui',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Yuchi: Coding = {
  code: 'x-YUC',
  display: 'Yuchi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Havasupai: Coding = {
  code: 'x-YUF',
  display: 'Havasupai',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Yuki: Coding = {
  code: 'x-YUK',
  display: 'Yuki',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Quechan: Coding = {
  code: 'x-YUM',
  display: 'Quechan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Yurok: Coding = {
  code: 'x-YUR',
  display: 'Yurok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
const v3_AmericanIndianAlaskaNativeLanguages_Zuni: Coding = {
  code: 'x-ZUN',
  display: 'Zuni',
  system: 'http://terminology.hl7.org/CodeSystem/v3-AmericanIndianAlaskaNativeLanguages',
};
/**
 *  American Indian and Alaska Native languages currently being used in the United States.
 */
export const V3AmericanIndianAlaskaNativeLanguages = {
  /**
   * Abenakian
   */
  Abenakian: v3_AmericanIndianAlaskaNativeLanguages_Abenakian,
  /**
   * Aleut
   */
  Aleut: v3_AmericanIndianAlaskaNativeLanguages_Aleut,
  /**
   * Algic
   */
  Algic: v3_AmericanIndianAlaskaNativeLanguages_Algic,
  /**
   * Algonquian
   */
  Algonquian: v3_AmericanIndianAlaskaNativeLanguages_Algonquian,
  /**
   * Apachean
   */
  Apachean: v3_AmericanIndianAlaskaNativeLanguages_Apachean,
  /**
   * Arapahoan
   */
  Arapahoan: v3_AmericanIndianAlaskaNativeLanguages_Arapahoan,
  /**
   * ArapahoGrosVentre
   */
  ArapahoGrosVentre: v3_AmericanIndianAlaskaNativeLanguages_ArapahoGrosVentre,
  /**
   * Athapaskan
   */
  Athapaskan: v3_AmericanIndianAlaskaNativeLanguages_Athapaskan,
  /**
   * AthapaskanEyak
   */
  AthapaskanEyak: v3_AmericanIndianAlaskaNativeLanguages_AthapaskanEyak,
  /**
   * Caddoan
   */
  Caddoan: v3_AmericanIndianAlaskaNativeLanguages_Caddoan,
  /**
   * Cahitan
   */
  Cahitan: v3_AmericanIndianAlaskaNativeLanguages_Cahitan,
  /**
   * CaliforniaAthapaskan
   */
  CaliforniaAthapaskan: v3_AmericanIndianAlaskaNativeLanguages_CaliforniaAthapaskan,
  /**
   * CentralAlaskaYukon
   */
  CentralAlaskaYukon: v3_AmericanIndianAlaskaNativeLanguages_CentralAlaskaYukon,
  /**
   * CentralMuskogean
   */
  CentralMuskogean: v3_AmericanIndianAlaskaNativeLanguages_CentralMuskogean,
  /**
   * CentralNumic
   */
  CentralNumic: v3_AmericanIndianAlaskaNativeLanguages_CentralNumic,
  /**
   * CentralSalish
   */
  CentralSalish: v3_AmericanIndianAlaskaNativeLanguages_CentralSalish,
  /**
   * Chimakuan
   */
  Chimakuan: v3_AmericanIndianAlaskaNativeLanguages_Chimakuan,
  /**
   * Chinookan
   */
  Chinookan: v3_AmericanIndianAlaskaNativeLanguages_Chinookan,
  /**
   * ChiwereWinnebago
   */
  ChiwereWinnebago: v3_AmericanIndianAlaskaNativeLanguages_ChiwereWinnebago,
  /**
   * CochimiYuman
   */
  CochimiYuman: v3_AmericanIndianAlaskaNativeLanguages_CochimiYuman,
  /**
   * Coosan
   */
  Coosan: v3_AmericanIndianAlaskaNativeLanguages_Coosan,
  /**
   * Cree
   */
  Cree: v3_AmericanIndianAlaskaNativeLanguages_Cree,
  /**
   * CreeMontagnais
   */
  CreeMontagnais: v3_AmericanIndianAlaskaNativeLanguages_CreeMontagnais,
  /**
   * Cupan
   */
  Cupan: v3_AmericanIndianAlaskaNativeLanguages_Cupan,
  /**
   * Dakotan
   */
  Dakotan: v3_AmericanIndianAlaskaNativeLanguages_Dakotan,
  /**
   * Delawaran
   */
  Delawaran: v3_AmericanIndianAlaskaNativeLanguages_Delawaran,
  /**
   * DeltaCalifornia
   */
  DeltaCalifornia: v3_AmericanIndianAlaskaNativeLanguages_DeltaCalifornia,
  /**
   * Dhegiha
   */
  Dhegiha: v3_AmericanIndianAlaskaNativeLanguages_Dhegiha,
  /**
   * Diegueno
   */
  Diegueno: v3_AmericanIndianAlaskaNativeLanguages_Diegueno,
  /**
   * EasternAlgonquin
   */
  EasternAlgonquin: v3_AmericanIndianAlaskaNativeLanguages_EasternAlgonquin,
  /**
   * EasternApachean
   */
  EasternApachean: v3_AmericanIndianAlaskaNativeLanguages_EasternApachean,
  /**
   * EasternMiwok
   */
  EasternMiwok: v3_AmericanIndianAlaskaNativeLanguages_EasternMiwok,
  /**
   * EskimoAleut
   */
  EskimoAleut: v3_AmericanIndianAlaskaNativeLanguages_EskimoAleut,
  /**
   * Eskimoan
   */
  Eskimoan: v3_AmericanIndianAlaskaNativeLanguages_Eskimoan,
  /**
   * Hokan
   */
  Hokan: v3_AmericanIndianAlaskaNativeLanguages_Hokan,
  /**
   * InteriorSalish
   */
  InteriorSalish: v3_AmericanIndianAlaskaNativeLanguages_InteriorSalish,
  /**
   * InuitInupiaq
   */
  InuitInupiaq: v3_AmericanIndianAlaskaNativeLanguages_InuitInupiaq,
  /**
   * Iroquoian
   */
  Iroquoian: v3_AmericanIndianAlaskaNativeLanguages_Iroquoian,
  /**
   * Kalapuyan
   */
  Kalapuyan: v3_AmericanIndianAlaskaNativeLanguages_Kalapuyan,
  /**
   * Keresan
   */
  Keresan: v3_AmericanIndianAlaskaNativeLanguages_Keresan,
  /**
   * KiowaTanoan
   */
  KiowaTanoan: v3_AmericanIndianAlaskaNativeLanguages_KiowaTanoan,
  /**
   * KoyukonIngalik
   */
  KoyukonIngalik: v3_AmericanIndianAlaskaNativeLanguages_KoyukonIngalik,
  /**
   * KutchinHan
   */
  KutchinHan: v3_AmericanIndianAlaskaNativeLanguages_KutchinHan,
  /**
   * Maiduan
   */
  Maiduan: v3_AmericanIndianAlaskaNativeLanguages_Maiduan,
  /**
   * MississippiValley
   */
  MississippiValley: v3_AmericanIndianAlaskaNativeLanguages_MississippiValley,
  /**
   * MissouriRiver
   */
  MissouriRiver: v3_AmericanIndianAlaskaNativeLanguages_MissouriRiver,
  /**
   * Miwokan
   */
  Miwokan: v3_AmericanIndianAlaskaNativeLanguages_Miwokan,
  /**
   * Muskogean
   */
  Muskogean: v3_AmericanIndianAlaskaNativeLanguages_Muskogean,
  /**
   * Nadene
   */
  Nadene: v3_AmericanIndianAlaskaNativeLanguages_Nadene,
  /**
   * Nootkan
   */
  Nootkan: v3_AmericanIndianAlaskaNativeLanguages_Nootkan,
  /**
   * NorthernCaddoan
   */
  NorthernCaddoan: v3_AmericanIndianAlaskaNativeLanguages_NorthernCaddoan,
  /**
   * NorthernIroquoian
   */
  NorthernIroquoian: v3_AmericanIndianAlaskaNativeLanguages_NorthernIroquoian,
  /**
   * Numic
   */
  Numic: v3_AmericanIndianAlaskaNativeLanguages_Numic,
  /**
   * Ojibwayan
   */
  Ojibwayan: v3_AmericanIndianAlaskaNativeLanguages_Ojibwayan,
  /**
   * OregonAthapaskan
   */
  OregonAthapaskan: v3_AmericanIndianAlaskaNativeLanguages_OregonAthapaskan,
  /**
   * PacificCoastAthapaskan
   */
  PacificCoastAthapaskan: v3_AmericanIndianAlaskaNativeLanguages_PacificCoastAthapaskan,
  /**
   * Pai
   */
  Pai: v3_AmericanIndianAlaskaNativeLanguages_Pai,
  /**
   * Palaihnihan
   */
  Palaihnihan: v3_AmericanIndianAlaskaNativeLanguages_Palaihnihan,
  /**
   * Penutian
   */
  Penutian: v3_AmericanIndianAlaskaNativeLanguages_Penutian,
  /**
   * Pidgin
   */
  Pidgin: v3_AmericanIndianAlaskaNativeLanguages_Pidgin,
  /**
   * PlateauPenutian
   */
  PlateauPenutian: v3_AmericanIndianAlaskaNativeLanguages_PlateauPenutian,
  /**
   * Pomoan
   */
  Pomoan: v3_AmericanIndianAlaskaNativeLanguages_Pomoan,
  /**
   * Ritwan
   */
  Ritwan: v3_AmericanIndianAlaskaNativeLanguages_Ritwan,
  /**
   * River
   */
  River: v3_AmericanIndianAlaskaNativeLanguages_River,
  /**
   * Sahaptian
   */
  Sahaptian: v3_AmericanIndianAlaskaNativeLanguages_Sahaptian,
  /**
   * Salishan
   */
  Salishan: v3_AmericanIndianAlaskaNativeLanguages_Salishan,
  /**
   * SaukFoxKickapoo
   */
  SaukFoxKickapoo: v3_AmericanIndianAlaskaNativeLanguages_SaukFoxKickapoo,
  /**
   * SerranoGabrielino
   */
  SerranoGabrielino: v3_AmericanIndianAlaskaNativeLanguages_SerranoGabrielino,
  /**
   * Shasta
   */
  Shasta: v3_AmericanIndianAlaskaNativeLanguages_Shasta,
  /**
   * Siouan
   */
  Siouan: v3_AmericanIndianAlaskaNativeLanguages_Siouan,
  /**
   * SiouanCatawba
   */
  SiouanCatawba: v3_AmericanIndianAlaskaNativeLanguages_SiouanCatawba,
  /**
   * SirenikskiYupik
   */
  SirenikskiYupik: v3_AmericanIndianAlaskaNativeLanguages_SirenikskiYupik,
  /**
   * SouthernAlaska
   */
  SouthernAlaska: v3_AmericanIndianAlaskaNativeLanguages_SouthernAlaska,
  /**
   * SouthernCaddoan
   */
  SouthernCaddoan: v3_AmericanIndianAlaskaNativeLanguages_SouthernCaddoan,
  /**
   * SouthernNumic
   */
  SouthernNumic: v3_AmericanIndianAlaskaNativeLanguages_SouthernNumic,
  /**
   * Takelman
   */
  Takelman: v3_AmericanIndianAlaskaNativeLanguages_Takelman,
  /**
   * Takic
   */
  Takic: v3_AmericanIndianAlaskaNativeLanguages_Takic,
  /**
   * Tanana
   */
  Tanana: v3_AmericanIndianAlaskaNativeLanguages_Tanana,
  /**
   * TananaTutchone
   */
  TananaTutchone: v3_AmericanIndianAlaskaNativeLanguages_TananaTutchone,
  /**
   * Taracahitan
   */
  Taracahitan: v3_AmericanIndianAlaskaNativeLanguages_Taracahitan,
  /**
   * Tepiman
   */
  Tepiman: v3_AmericanIndianAlaskaNativeLanguages_Tepiman,
  /**
   * Tiwa
   */
  Tiwa: v3_AmericanIndianAlaskaNativeLanguages_Tiwa,
  /**
   * Tsamosan
   */
  Tsamosan: v3_AmericanIndianAlaskaNativeLanguages_Tsamosan,
  /**
   * Tsimshianic
   */
  Tsimshianic: v3_AmericanIndianAlaskaNativeLanguages_Tsimshianic,
  /**
   * UpperChinook
   */
  UpperChinook: v3_AmericanIndianAlaskaNativeLanguages_UpperChinook,
  /**
   * Utian
   */
  Utian: v3_AmericanIndianAlaskaNativeLanguages_Utian,
  /**
   * UtoAztecan
   */
  UtoAztecan: v3_AmericanIndianAlaskaNativeLanguages_UtoAztecan,
  /**
   * Wakashan
   */
  Wakashan: v3_AmericanIndianAlaskaNativeLanguages_Wakashan,
  /**
   * WesternApachean
   */
  WesternApachean: v3_AmericanIndianAlaskaNativeLanguages_WesternApachean,
  /**
   * WesternMiwok
   */
  WesternMiwok: v3_AmericanIndianAlaskaNativeLanguages_WesternMiwok,
  /**
   * WesternMuskogean
   */
  WesternMuskogean: v3_AmericanIndianAlaskaNativeLanguages_WesternMuskogean,
  /**
   * WesternNumic
   */
  WesternNumic: v3_AmericanIndianAlaskaNativeLanguages_WesternNumic,
  /**
   * Wintuan
   */
  Wintuan: v3_AmericanIndianAlaskaNativeLanguages_Wintuan,
  /**
   * Yokutsan
   */
  Yokutsan: v3_AmericanIndianAlaskaNativeLanguages_Yokutsan,
  /**
   * Yukian
   */
  Yukian: v3_AmericanIndianAlaskaNativeLanguages_Yukian,
  /**
   * Yuman
   */
  Yuman: v3_AmericanIndianAlaskaNativeLanguages_Yuman,
  /**
   * Eastern Abenaki
   */
  EasternAbenaki: v3_AmericanIndianAlaskaNativeLanguages_EasternAbenaki,
  /**
   * Western Abenaki
   */
  WesternAbenaki: v3_AmericanIndianAlaskaNativeLanguages_WesternAbenaki,
  /**
   * Achumawi
   */
  Achumawi: v3_AmericanIndianAlaskaNativeLanguages_Achumawi,
  /**
   * Ahtna
   */
  Ahtna: v3_AmericanIndianAlaskaNativeLanguages_Ahtna,
  /**
   * Alabama
   */
  Alabama: v3_AmericanIndianAlaskaNativeLanguages_Alabama,
  /**
   * Aleut
   */
  Aleut_2: v3_AmericanIndianAlaskaNativeLanguages_Aleut_2,
  /**
   * Jicarilla
   */
  Jicarilla: v3_AmericanIndianAlaskaNativeLanguages_Jicarilla,
  /**
   * Kiowa Apache
   */
  KiowaApache: v3_AmericanIndianAlaskaNativeLanguages_KiowaApache,
  /**
   * Lipan
   */
  Lipan: v3_AmericanIndianAlaskaNativeLanguages_Lipan,
  /**
   * Mescalero-Chiricahua
   */
  MescaleroChiricahua: v3_AmericanIndianAlaskaNativeLanguages_MescaleroChiricahua,
  /**
   * Western Apache
   */
  WesternApache: v3_AmericanIndianAlaskaNativeLanguages_WesternApache,
  /**
   * Arikara
   */
  Arikara: v3_AmericanIndianAlaskaNativeLanguages_Arikara,
  /**
   * Arapaho
   */
  Arapaho: v3_AmericanIndianAlaskaNativeLanguages_Arapaho,
  /**
   * Assiniboine
   */
  Assiniboine: v3_AmericanIndianAlaskaNativeLanguages_Assiniboine,
  /**
   * Gros Ventre
   */
  GrosVentre: v3_AmericanIndianAlaskaNativeLanguages_GrosVentre,
  /**
   * Atsugewi
   */
  Atsugewi: v3_AmericanIndianAlaskaNativeLanguages_Atsugewi,
  /**
   * Blackfoot
   */
  Blackfoot: v3_AmericanIndianAlaskaNativeLanguages_Blackfoot,
  /**
   * Caddo
   */
  Caddo: v3_AmericanIndianAlaskaNativeLanguages_Caddo,
  /**
   * Cayuga
   */
  Cayuga: v3_AmericanIndianAlaskaNativeLanguages_Cayuga,
  /**
   * Choctaw
   */
  Choctaw: v3_AmericanIndianAlaskaNativeLanguages_Choctaw,
  /**
   * Lower Chehalis
   */
  LowerChehalis: v3_AmericanIndianAlaskaNativeLanguages_LowerChehalis,
  /**
   * Cherokee
   */
  Cherokee: v3_AmericanIndianAlaskaNativeLanguages_Cherokee,
  /**
   * Chinook Wawa
   */
  ChinookWawa: v3_AmericanIndianAlaskaNativeLanguages_ChinookWawa,
  /**
   * Cahuilla
   */
  Cahuilla: v3_AmericanIndianAlaskaNativeLanguages_Cahuilla,
  /**
   * Cheyenne
   */
  Cheyenne: v3_AmericanIndianAlaskaNativeLanguages_Cheyenne,
  /**
   * Chickasaw
   */
  Chickasaw: v3_AmericanIndianAlaskaNativeLanguages_Chickasaw,
  /**
   * Upper Chehalis
   */
  UpperChehalis: v3_AmericanIndianAlaskaNativeLanguages_UpperChehalis,
  /**
   * Koasati
   */
  Koasati: v3_AmericanIndianAlaskaNativeLanguages_Koasati,
  /**
   * Clallam
   */
  Clallam: v3_AmericanIndianAlaskaNativeLanguages_Clallam,
  /**
   * Cocopa
   */
  Cocopa: v3_AmericanIndianAlaskaNativeLanguages_Cocopa,
  /**
   * Columbian
   */
  Columbian: v3_AmericanIndianAlaskaNativeLanguages_Columbian,
  /**
   * Comanche
   */
  Comanche: v3_AmericanIndianAlaskaNativeLanguages_Comanche,
  /**
   * Hanis
   */
  Hanis: v3_AmericanIndianAlaskaNativeLanguages_Hanis,
  /**
   * Cowlitz
   */
  Cowlitz: v3_AmericanIndianAlaskaNativeLanguages_Cowlitz,
  /**
   * Coeur D'alene
   */
  CoeurDQuoteAlene: v3_AmericanIndianAlaskaNativeLanguages_CoeurDQuoteAlene,
  /**
   * Creek
   */
  Creek: v3_AmericanIndianAlaskaNativeLanguages_Creek,
  /**
   * Crow
   */
  Crow: v3_AmericanIndianAlaskaNativeLanguages_Crow,
  /**
   * Western Cree
   */
  WesternCree: v3_AmericanIndianAlaskaNativeLanguages_WesternCree,
  /**
   * Coast Miwok
   */
  CoastMiwok: v3_AmericanIndianAlaskaNativeLanguages_CoastMiwok,
  /**
   * Central Sierra Miwok
   */
  CentralSierraMiwok: v3_AmericanIndianAlaskaNativeLanguages_CentralSierraMiwok,
  /**
   * Cupeno
   */
  Cupeno: v3_AmericanIndianAlaskaNativeLanguages_Cupeno,
  /**
   * Unami Delaware
   */
  UnamiDelaware: v3_AmericanIndianAlaskaNativeLanguages_UnamiDelaware,
  /**
   * Dakota
   */
  Dakota: v3_AmericanIndianAlaskaNativeLanguages_Dakota,
  /**
   * Kumeyaay
   */
  Kumeyaay: v3_AmericanIndianAlaskaNativeLanguages_Kumeyaay,
  /**
   * Pacific Yupik Gulf
   */
  PacificYupikGulf: v3_AmericanIndianAlaskaNativeLanguages_PacificYupikGulf,
  /**
   * Kings River
   */
  KingsRiver: v3_AmericanIndianAlaskaNativeLanguages_KingsRiver,
  /**
   * North Alaskan Inuktitut
   */
  NorthAlaskanInuktitut: v3_AmericanIndianAlaskaNativeLanguages_NorthAlaskanInuktitut,
  /**
   * Northwest Alaska Inuktitut
   */
  NorthwestAlaskaInuktitut: v3_AmericanIndianAlaskaNativeLanguages_NorthwestAlaskaInuktitut,
  /**
   * Central Siberian Yupik
   */
  CentralSiberianYupik: v3_AmericanIndianAlaskaNativeLanguages_CentralSiberianYupik,
  /**
   * Central Alaskan Yupik
   */
  CentralAlaskanYupik: v3_AmericanIndianAlaskaNativeLanguages_CentralAlaskanYupik,
  /**
   * Eyak
   */
  Eyak: v3_AmericanIndianAlaskaNativeLanguages_Eyak,
  /**
   * Kalispel
   */
  Kalispel: v3_AmericanIndianAlaskaNativeLanguages_Kalispel,
  /**
   * Gashowu
   */
  Gashowu: v3_AmericanIndianAlaskaNativeLanguages_Gashowu,
  /**
   * Han
   */
  Han: v3_AmericanIndianAlaskaNativeLanguages_Han,
  /**
   * Haida
   */
  Haida: v3_AmericanIndianAlaskaNativeLanguages_Haida,
  /**
   * Hidatsa
   */
  Hidatsa: v3_AmericanIndianAlaskaNativeLanguages_Hidatsa,
  /**
   * Holikachuk
   */
  Holikachuk: v3_AmericanIndianAlaskaNativeLanguages_Holikachuk,
  /**
   * Hopi
   */
  Hopi: v3_AmericanIndianAlaskaNativeLanguages_Hopi,
  /**
   * Hupa
   */
  Hupa: v3_AmericanIndianAlaskaNativeLanguages_Hupa,
  /**
   * Degexit'an
   */
  DegexitQuoteAn: v3_AmericanIndianAlaskaNativeLanguages_DegexitQuoteAn,
  /**
   * Chiwere
   */
  Chiwere: v3_AmericanIndianAlaskaNativeLanguages_Chiwere,
  /**
   * Kansa
   */
  Kansa: v3_AmericanIndianAlaskaNativeLanguages_Kansa,
  /**
   * Central Kalapuyan
   */
  CentralKalapuyan: v3_AmericanIndianAlaskaNativeLanguages_CentralKalapuyan,
  /**
   * Kawaiisu
   */
  Kawaiisu: v3_AmericanIndianAlaskaNativeLanguages_Kawaiisu,
  /**
   * Rio Grande Keresan
   */
  RioGrandeKeresan: v3_AmericanIndianAlaskaNativeLanguages_RioGrandeKeresan,
  /**
   * Kickapoo
   */
  Kickapoo: v3_AmericanIndianAlaskaNativeLanguages_Kickapoo,
  /**
   * Kiowa
   */
  Kiowa: v3_AmericanIndianAlaskaNativeLanguages_Kiowa,
  /**
   * Acoma-Laguna
   */
  AcomaLaguna: v3_AmericanIndianAlaskaNativeLanguages_AcomaLaguna,
  /**
   * Kashaya
   */
  Kashaya: v3_AmericanIndianAlaskaNativeLanguages_Kashaya,
  /**
   * Klamath-Modoc
   */
  KlamathModoc: v3_AmericanIndianAlaskaNativeLanguages_KlamathModoc,
  /**
   * Koyukon
   */
  Koyukon: v3_AmericanIndianAlaskaNativeLanguages_Koyukon,
  /**
   * Cahto
   */
  Cahto: v3_AmericanIndianAlaskaNativeLanguages_Cahto,
  /**
   * Kutchin
   */
  Kutchin: v3_AmericanIndianAlaskaNativeLanguages_Kutchin,
  /**
   * Kootenai
   */
  Kootenai: v3_AmericanIndianAlaskaNativeLanguages_Kootenai,
  /**
   * Upper Kuskokwim
   */
  UpperKuskokwim: v3_AmericanIndianAlaskaNativeLanguages_UpperKuskokwim,
  /**
   * Karok
   */
  Karok: v3_AmericanIndianAlaskaNativeLanguages_Karok,
  /**
   * Lakota
   */
  Lakota: v3_AmericanIndianAlaskaNativeLanguages_Lakota,
  /**
   * Lake Miwok
   */
  LakeMiwok: v3_AmericanIndianAlaskaNativeLanguages_LakeMiwok,
  /**
   * Luiseno
   */
  Luiseno: v3_AmericanIndianAlaskaNativeLanguages_Luiseno,
  /**
   * Lushootseed
   */
  Lushootseed: v3_AmericanIndianAlaskaNativeLanguages_Lushootseed,
  /**
   * Maliseet-Passamaquoddy
   */
  MaliseetPassamaquoddy: v3_AmericanIndianAlaskaNativeLanguages_MaliseetPassamaquoddy,
  /**
   * Northwest Maidu
   */
  NorthwestMaidu: v3_AmericanIndianAlaskaNativeLanguages_NorthwestMaidu,
  /**
   * Menominee
   */
  Menominee: v3_AmericanIndianAlaskaNativeLanguages_Menominee,
  /**
   * Mandan
   */
  Mandan: v3_AmericanIndianAlaskaNativeLanguages_Mandan,
  /**
   * Micmac
   */
  Micmac: v3_AmericanIndianAlaskaNativeLanguages_Micmac,
  /**
   * Mikasuki
   */
  Mikasuki: v3_AmericanIndianAlaskaNativeLanguages_Mikasuki,
  /**
   * Mohawk
   */
  Mohawk: v3_AmericanIndianAlaskaNativeLanguages_Mohawk,
  /**
   * Mono
   */
  Mono: v3_AmericanIndianAlaskaNativeLanguages_Mono,
  /**
   * Mohave
   */
  Mohave: v3_AmericanIndianAlaskaNativeLanguages_Mohave,
  /**
   * Maricopa
   */
  Maricopa: v3_AmericanIndianAlaskaNativeLanguages_Maricopa,
  /**
   * Makah
   */
  Makah: v3_AmericanIndianAlaskaNativeLanguages_Makah,
  /**
   * Dine
   */
  Dine: v3_AmericanIndianAlaskaNativeLanguages_Dine,
  /**
   * Nez Perce
   */
  NezPerce: v3_AmericanIndianAlaskaNativeLanguages_NezPerce,
  /**
   * Nakota
   */
  Nakota: v3_AmericanIndianAlaskaNativeLanguages_Nakota,
  /**
   * Northeast Maidu
   */
  NortheastMaidu: v3_AmericanIndianAlaskaNativeLanguages_NortheastMaidu,
  /**
   * Northern Sierra Miwok
   */
  NorthernSierraMiwok: v3_AmericanIndianAlaskaNativeLanguages_NorthernSierraMiwok,
  /**
   * Nisenan
   */
  Nisenan: v3_AmericanIndianAlaskaNativeLanguages_Nisenan,
  /**
   * Southern Ojibwa
   */
  SouthernOjibwa: v3_AmericanIndianAlaskaNativeLanguages_SouthernOjibwa,
  /**
   * Okanagan
   */
  Okanagan: v3_AmericanIndianAlaskaNativeLanguages_Okanagan,
  /**
   * Omaha-Ponca
   */
  OmahaPonca: v3_AmericanIndianAlaskaNativeLanguages_OmahaPonca,
  /**
   * Oneida
   */
  Oneida: v3_AmericanIndianAlaskaNativeLanguages_Oneida,
  /**
   * Onondaga
   */
  Onondaga: v3_AmericanIndianAlaskaNativeLanguages_Onondaga,
  /**
   * Osage
   */
  Osage: v3_AmericanIndianAlaskaNativeLanguages_Osage,
  /**
   * Northern Paiute-Bannock
   */
  NorthernPaiuteBannock: v3_AmericanIndianAlaskaNativeLanguages_NorthernPaiuteBannock,
  /**
   * Papago-Pima
   */
  PapagoPima: v3_AmericanIndianAlaskaNativeLanguages_PapagoPima,
  /**
   * Panamint
   */
  Panamint: v3_AmericanIndianAlaskaNativeLanguages_Panamint,
  /**
   * Pawnee
   */
  Pawnee: v3_AmericanIndianAlaskaNativeLanguages_Pawnee,
  /**
   * Northeastern Pomo
   */
  NortheasternPomo: v3_AmericanIndianAlaskaNativeLanguages_NortheasternPomo,
  /**
   * Southeastern Pomo
   */
  SoutheasternPomo: v3_AmericanIndianAlaskaNativeLanguages_SoutheasternPomo,
  /**
   * Southern Pomo
   */
  SouthernPomo: v3_AmericanIndianAlaskaNativeLanguages_SouthernPomo,
  /**
   * Plains Miwok
   */
  PlainsMiwok: v3_AmericanIndianAlaskaNativeLanguages_PlainsMiwok,
  /**
   * Central Pomo
   */
  CentralPomo: v3_AmericanIndianAlaskaNativeLanguages_CentralPomo,
  /**
   * Potawatami
   */
  Potawatami: v3_AmericanIndianAlaskaNativeLanguages_Potawatami,
  /**
   * Plains Indian Sign Language
   */
  PlainsIndianSignLanguage: v3_AmericanIndianAlaskaNativeLanguages_PlainsIndianSignLanguage,
  /**
   * Poso Creek
   */
  PosoCreek: v3_AmericanIndianAlaskaNativeLanguages_PosoCreek,
  /**
   * Quapaw
   */
  Quapaw: v3_AmericanIndianAlaskaNativeLanguages_Quapaw,
  /**
   * Quileute
   */
  Quileute: v3_AmericanIndianAlaskaNativeLanguages_Quileute,
  /**
   * Quinault
   */
  Quinault: v3_AmericanIndianAlaskaNativeLanguages_Quinault,
  /**
   * Mesquakie
   */
  Mesquakie: v3_AmericanIndianAlaskaNativeLanguages_Mesquakie,
  /**
   * Seneca
   */
  Seneca: v3_AmericanIndianAlaskaNativeLanguages_Seneca,
  /**
   * Serrano
   */
  Serrano: v3_AmericanIndianAlaskaNativeLanguages_Serrano,
  /**
   * Shoshone
   */
  Shoshone: v3_AmericanIndianAlaskaNativeLanguages_Shoshone,
  /**
   * Shasta
   */
  Shasta_2: v3_AmericanIndianAlaskaNativeLanguages_Shasta_2,
  /**
   * Shawnee
   */
  Shawnee: v3_AmericanIndianAlaskaNativeLanguages_Shawnee,
  /**
   * Southern Sierra Miwok
   */
  SouthernSierraMiwok: v3_AmericanIndianAlaskaNativeLanguages_SouthernSierraMiwok,
  /**
   * Northern Straits
   */
  NorthernStraits: v3_AmericanIndianAlaskaNativeLanguages_NorthernStraits,
  /**
   * Lower Tanana
   */
  LowerTanana: v3_AmericanIndianAlaskaNativeLanguages_LowerTanana,
  /**
   * Northern Tiwa
   */
  NorthernTiwa: v3_AmericanIndianAlaskaNativeLanguages_NorthernTiwa,
  /**
   * Upper Tanana
   */
  UpperTanana: v3_AmericanIndianAlaskaNativeLanguages_UpperTanana,
  /**
   * Tanacross
   */
  Tanacross: v3_AmericanIndianAlaskaNativeLanguages_Tanacross,
  /**
   * Tewa
   */
  Tewa: v3_AmericanIndianAlaskaNativeLanguages_Tewa,
  /**
   * Tanaina
   */
  Tanaina: v3_AmericanIndianAlaskaNativeLanguages_Tanaina,
  /**
   * Southern Tiwa
   */
  SouthernTiwa: v3_AmericanIndianAlaskaNativeLanguages_SouthernTiwa,
  /**
   * Tule-Kaweah
   */
  TuleKaweah: v3_AmericanIndianAlaskaNativeLanguages_TuleKaweah,
  /**
   * Tlingit
   */
  Tlingit: v3_AmericanIndianAlaskaNativeLanguages_Tlingit,
  /**
   * Tolowa
   */
  Tolowa: v3_AmericanIndianAlaskaNativeLanguages_Tolowa,
  /**
   * Jemez
   */
  Jemez: v3_AmericanIndianAlaskaNativeLanguages_Jemez,
  /**
   * Coast Tsimshain
   */
  CoastTsimshain: v3_AmericanIndianAlaskaNativeLanguages_CoastTsimshain,
  /**
   * Tubatululabal
   */
  Tubatululabal: v3_AmericanIndianAlaskaNativeLanguages_Tubatululabal,
  /**
   * Tuscarora
   */
  Tuscarora: v3_AmericanIndianAlaskaNativeLanguages_Tuscarora,
  /**
   * Tututni
   */
  Tututni: v3_AmericanIndianAlaskaNativeLanguages_Tututni,
  /**
   * Umatilla
   */
  Umatilla: v3_AmericanIndianAlaskaNativeLanguages_Umatilla,
  /**
   * Ute-Southern Paiute
   */
  UteSouthernPaiute: v3_AmericanIndianAlaskaNativeLanguages_UteSouthernPaiute,
  /**
   * Walla Walla
   */
  WallaWalla: v3_AmericanIndianAlaskaNativeLanguages_WallaWalla,
  /**
   * Kiksht
   */
  Kiksht: v3_AmericanIndianAlaskaNativeLanguages_Kiksht,
  /**
   * Wappo
   */
  Wappo: v3_AmericanIndianAlaskaNativeLanguages_Wappo,
  /**
   * Tenino
   */
  Tenino: v3_AmericanIndianAlaskaNativeLanguages_Tenino,
  /**
   * Washoe
   */
  Washoe: v3_AmericanIndianAlaskaNativeLanguages_Washoe,
  /**
   * Wichita
   */
  Wichita: v3_AmericanIndianAlaskaNativeLanguages_Wichita,
  /**
   * Hocak
   */
  Hocak: v3_AmericanIndianAlaskaNativeLanguages_Hocak,
  /**
   * Wintu-Nomlaki
   */
  WintuNomlaki: v3_AmericanIndianAlaskaNativeLanguages_WintuNomlaki,
  /**
   * Yakima
   */
  Yakima: v3_AmericanIndianAlaskaNativeLanguages_Yakima,
  /**
   * Yaqui
   */
  Yaqui: v3_AmericanIndianAlaskaNativeLanguages_Yaqui,
  /**
   * Yuchi
   */
  Yuchi: v3_AmericanIndianAlaskaNativeLanguages_Yuchi,
  /**
   * Havasupai
   */
  Havasupai: v3_AmericanIndianAlaskaNativeLanguages_Havasupai,
  /**
   * Yuki
   */
  Yuki: v3_AmericanIndianAlaskaNativeLanguages_Yuki,
  /**
   * Quechan
   */
  Quechan: v3_AmericanIndianAlaskaNativeLanguages_Quechan,
  /**
   * Yurok
   */
  Yurok: v3_AmericanIndianAlaskaNativeLanguages_Yurok,
  /**
   * Zuni
   */
  Zuni: v3_AmericanIndianAlaskaNativeLanguages_Zuni,
};
const v3_Calendar_Gregorian: Coding = {
  code: 'GREG',
  display: 'Gregorian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Calendar',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3Calendar = {
  /**
   * The Gregorian calendar is the calendar in effect in most countries of Christian influence since approximately 1582. This calendar superceded the Julian calendar.
   */
  Gregorian: v3_Calendar_Gregorian,
};
const v3_CalendarCycle_WeekOfTheMonth: Coding = {
  code: 'WM',
  display: 'week of the month',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CalendarCycle',
};
/**
 *  Calendar cycle identifiers
 */
export const V3CalendarCycle = {
  /**
   * The week with the month's first Thursday in it (analagous to the ISO 8601 definition for week of the year).
   */
  WeekOfTheMonth: v3_CalendarCycle_WeekOfTheMonth,
};
const v3_CalendarType_Gregorian: Coding = {
  code: 'GREG',
  display: 'Gregorian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CalendarType',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3CalendarType = {
  /**
   * The Gregorian calendar is in effect in the most countries of Christian influence since approximately 1582.  This calendar superceded the Julian calendar.
   */
  Gregorian: v3_CalendarType_Gregorian,
};
const v3_Charset_EBCDIC: Coding = {
  code: 'EBCDIC',
  display: 'EBCDIC',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Charset',
};
const v3_Charset_ISO10646UCS2: Coding = {
  code: 'ISO-10646-UCS-2',
  display: 'ISO-10646-UCS-2',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Charset',
};
const v3_Charset_ISO10646UCS4: Coding = {
  code: 'ISO-10646-UCS-4',
  display: 'ISO-10646-UCS-4',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Charset',
};
const v3_Charset_ISO88591: Coding = {
  code: 'ISO-8859-1',
  display: 'ISO-8859-1',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Charset',
};
const v3_Charset_ISO88592: Coding = {
  code: 'ISO-8859-2',
  display: 'ISO-8859-2',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Charset',
};
const v3_Charset_ISO88595: Coding = {
  code: 'ISO-8859-5',
  display: 'ISO-8859-5',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Charset',
};
const v3_Charset_JIS2022JP: Coding = {
  code: 'JIS-2022-JP',
  display: 'JIS-2022-JP',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Charset',
};
const v3_Charset_USASCII: Coding = {
  code: 'US-ASCII',
  display: 'US-ASCII',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Charset',
};
const v3_Charset_UTF7: Coding = {
  code: 'UTF-7',
  display: 'UTF-7',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Charset',
};
const v3_Charset_UTF8: Coding = {
  code: 'UTF-8',
  display: 'UTF-8',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Charset',
};
/**
 *  Internet Assigned Numbers Authority (IANA) Charset Types
 */
export const V3Charset = {
  /**
   * HL7 is indifferent to the use of this Charset.
   */
  EBCDIC: v3_Charset_EBCDIC,
  /**
   * Deprecated for HL7 use.
   */
  ISO10646UCS2: v3_Charset_ISO10646UCS2,
  /**
   * Deprecated for HL7 use.
   */
  ISO10646UCS4: v3_Charset_ISO10646UCS4,
  /**
   * HL7 is indifferent to the use of this Charset.
   */
  ISO88591: v3_Charset_ISO88591,
  /**
   * HL7 is indifferent to the use of this Charset.
   */
  ISO88592: v3_Charset_ISO88592,
  /**
   * HL7 is indifferent to the use of this Charset.
   */
  ISO88595: v3_Charset_ISO88595,
  /**
   * HL7 is indifferent to the use of this Charset.
   */
  JIS2022JP: v3_Charset_JIS2022JP,
  /**
   * Required for HL7 use.
   */
  USASCII: v3_Charset_USASCII,
  /**
   * HL7 is indifferent to the use of this Charset.
   */
  UTF7: v3_Charset_UTF7,
  /**
   * Required for Unicode support.
   */
  UTF8: v3_Charset_UTF8,
};
const v3_CodingRationale_HL7SpecifiedOrMandated: Coding = {
  code: 'HL7',
  display: 'HL7 Specified or Mandated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CodingRationale',
};
const v3_CodingRationale_OriginallyProducedCode: Coding = {
  code: 'O',
  display: 'originally produced code',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CodingRationale',
};
const v3_CodingRationale_OriginalAndRequired: Coding = {
  code: 'OR',
  display: 'original and required',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CodingRationale',
};
const v3_CodingRationale_PostCoded: Coding = {
  code: 'P',
  display: 'post-coded',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CodingRationale',
};
const v3_CodingRationale_PostCodedAndRequired: Coding = {
  code: 'PR',
  display: 'post-coded and required',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CodingRationale',
};
const v3_CodingRationale_Required: Coding = {
  code: 'R',
  display: 'required',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CodingRationale',
};
const v3_CodingRationale_BothHL7MandatedAndTheOriginalCode: Coding = {
  code: 'SH',
  display: 'Both HL7 mandated and the original code',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CodingRationale',
};
const v3_CodingRationale_SourceOrOriginalCode: Coding = {
  code: 'SRC',
  display: 'Source (or original) code',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CodingRationale',
};
/**
 *  Identifies how to interpret the instance of the code, codeSystem value in a set of translations.  Since HL7 (or a government body) may mandate that codes from certain code systems be sent in conformant messages, other synonyms that are sent in the translation set need to be distinguished among the originally captured source, the HL7 specified code, or some future role.  When this code is NULL, it indicates that the translation is an undefined type.  When valued, this property must contain one of the following values: SRC - Source (or original) code HL7 - HL7 Specified or Mandated SH - both HL7 mandated and the original code (precoordination) There may be additional values added to this value set as we work through the use of codes in messages and determine other Use Cases requiring special interpretation of the translations.
 */
export const V3CodingRationale = {
  /**
   * HL7 Specified or Mandated
   */
  HL7SpecifiedOrMandated: v3_CodingRationale_HL7SpecifiedOrMandated,
  /**
   * Description: Originally produced code.
   */
  OriginallyProducedCode: v3_CodingRationale_OriginallyProducedCode,
  /**
   * Originally produced code, required by the specification describing the use of the coded concept.
   */
  OriginalAndRequired: v3_CodingRationale_OriginalAndRequired,
  /**
   * Description: Post-coded from free text source</description>
   */
  PostCoded: v3_CodingRationale_PostCoded,
  /**
   * Post-coded from free text source, required by the specification describing the use of the coded concept.
   */
  PostCodedAndRequired: v3_CodingRationale_PostCodedAndRequired,
  /**
   * Description: Required standard code for HL7.
   */
  Required: v3_CodingRationale_Required,
  /**
   * Both HL7 mandated and the original code (precoordination)
   */
  BothHL7MandatedAndTheOriginalCode: v3_CodingRationale_BothHL7MandatedAndTheOriginalCode,
  /**
   * Source (or original) code
   */
  SourceOrOriginalCode: v3_CodingRationale_SourceOrOriginalCode,
};
const v3_CommunicationFunctionType_Receiver: Coding = {
  code: 'RCV',
  display: 'receiver',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CommunicationFunctionType',
};
const v3_CommunicationFunctionType_RespondTo: Coding = {
  code: 'RSP',
  display: 'respond to',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CommunicationFunctionType',
};
const v3_CommunicationFunctionType_Sender: Coding = {
  code: 'SND',
  display: 'sender',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CommunicationFunctionType',
};
/**
 *  Describes the type of communication function that the associated entity plays in the associated transmission.
 */
export const V3CommunicationFunctionType = {
  /**
   * The entity is the receiver of the transmission.
   */
  Receiver: v3_CommunicationFunctionType_Receiver,
  /**
   * The entity is the one to which the response or reply to the transmission should be sent.
   */
  RespondTo: v3_CommunicationFunctionType_RespondTo,
  /**
   * The entity is the sender of the transmission.
   */
  Sender: v3_CommunicationFunctionType_Sender,
};
/**
 *  A named tag set for metadata used to populate a security category label field that "segments" an IT resource per policy by indicating that access and use is restricted to members of a defined community or project. (HL7 Healthcare Privacy and Security Classification System)  Usage Note:
 * This is the healthcare analog to the US Intelligence Community's concept of a Special Access Program.  Compartment codes may be used in as a field value in an initiator's clearance to indicate permission to access and use an IT Resource with a security label having the same compartment value in security category label field. Map: Aligns with ISO 2382-8 definition of Compartment -  "A division of data into isolated blocks with separate security controls for the purpose of reducing risk."
 */
export const V3Compartment = {
  /**
   * A group of health care entities, which may include health care providers, care givers, hospitals, facilities, health plans, and other health care constituents who coordinate care for reimbursement based on quality metrics for improving outcomes and lowering costs, and may be authorized to access the consumer's health information because of membership in that group.
   *
   *                         Security Compartment Labels assigned to a consumer's information use in accountable care workflows should be met or exceeded by the Security Compartment attribute claimed by a participant in a an accountable care workflow who is requesting access to that information
   */
  AccountableCareOrganizationCompartment: v3_ActCode_AccountableCareOrganizationCompartment,
  /**
   * This is the healthcare analog to the US Intelligence Community's concept of a Special Access Program.  Compartment codes may be used in as a field value in an initiator's clearance to indicate permission to access and use an IT Resource with a security label having the same compartment value in security category label field.
   *
   *                         Map: Aligns with ISO 2382-8 definition of Compartment - "A division of data into isolated blocks with separate security controls for the purpose of reducing risk."
   */
  Compartment: v3_ActCode_Compartment,
  /**
   * Care coordination across participants in a care plan requires sharing of a healthcare consumer's information specific to that workflow.  A care team member should only have access to that information while participating in that workflow or for other authorized uses.
   *
   *                         Security Compartment Labels assigned to a consumer's information use in care coordination workflows should be met or exceeded by the Security Compartment attribute claimed by a participant in a care team member workflow who is requesting access to that information
   */
  CareTeamCompartment: v3_ActCode_CareTeamCompartment,
  /**
   * Financial management department members who have access to healthcare consumer information as part of a patient account, billing and claims workflows.
   *
   *                         Security Compartment Labels assigned to consumer information used in these workflows should be met or exceeded by the Security Compartment attribute claimed by a participant in a financial management workflow who is requesting access to that information.
   */
  FinancialManagementCompartment: v3_ActCode_FinancialManagementCompartment,
  /**
   * A security category label field value, which indicates that access and use of an IT resource is restricted to members of human resources department or workflow.
   */
  HumanResourceCompartment: v3_ActCode_HumanResourceCompartment,
  /**
   * Providers and care givers who have an established relationship per criteria determined by policy are considered to have an established care provision relations with a healthcare consumer, and may be authorized to access the consumer's health information because of that relationship.  Providers and care givers should only have access to that information while participating in legitimate relationship workflows or for other authorized uses.
   *
   *                         Security Compartment Labels assigned to a consumer's information use in legitimate relationship workflows should be met or exceeded by the Security Compartment attribute claimed by a participant in a legitimate relationship workflow who is requesting access to that information.
   */
  LegitimateRelationshipCompartment: v3_ActCode_LegitimateRelationshipCompartment,
  /**
   * Patient administration members who have access to healthcare consumer information as part of a patient administration workflows.
   *
   *                         Security Compartment Labels assigned to consumer information used in these workflows should be met or exceeded by the Security Compartment attribute claimed by a participant in a patient administration workflow who is requesting access to that information.
   */
  PatientAdministrationCompartment: v3_ActCode_PatientAdministrationCompartment,
  /**
   * A security category label field value, which indicates that access and use of an IT resource is restricted to members of a research project.
   */
  ResearchProjectCompartment: v3_ActCode_ResearchProjectCompartment,
  /**
   * A security category label field value, which indicates that access and use of an IT resource is restricted to members of records management department or workflow.
   */
  RecordsManagementCompartment: v3_ActCode_RecordsManagementCompartment,
};
const v3_CompressionAlgorithm_Bzip: Coding = {
  code: 'BZ',
  display: 'bzip',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CompressionAlgorithm',
};
const v3_CompressionAlgorithm_Deflate: Coding = {
  code: 'DF',
  display: 'deflate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CompressionAlgorithm',
};
const v3_CompressionAlgorithm_Gzip: Coding = {
  code: 'GZ',
  display: 'gzip',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CompressionAlgorithm',
};
const v3_CompressionAlgorithm_Compress: Coding = {
  code: 'Z',
  display: 'compress',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CompressionAlgorithm',
};
const v3_CompressionAlgorithm_Z7: Coding = {
  code: 'Z7',
  display: 'Z7',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CompressionAlgorithm',
};
const v3_CompressionAlgorithm_Zlib: Coding = {
  code: 'ZL',
  display: 'zlib',
  system: 'http://terminology.hl7.org/CodeSystem/v3-CompressionAlgorithm',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3CompressionAlgorithm = {
  /**
   * bzip-2 compression format. See [http://www.bzip.org/] for more information.
   */
  Bzip: v3_CompressionAlgorithm_Bzip,
  /**
   * The deflate compressed data format as specified in RFC 1951 [http://www.ietf.org/rfc/rfc1951.txt].
   */
  Deflate: v3_CompressionAlgorithm_Deflate,
  /**
   * A compressed data format that is compatible with the widely used GZIP utility as specified in RFC 1952 [http://www.ietf.org/rfc/rfc1952.txt] (uses the deflate algorithm).
   */
  Gzip: v3_CompressionAlgorithm_Gzip,
  /**
   * Original UNIX compress algorithm and file format using the LZC algorithm (a variant of LZW).  Patent encumbered and less efficient than deflate.
   */
  Compress: v3_CompressionAlgorithm_Compress,
  /**
   * 7z compression file format. See [http://www.7-zip.org/7z.html] for more information.
   */
  Z7: v3_CompressionAlgorithm_Z7,
  /**
   * A compressed data format that also uses the deflate algorithm.  Specified as RFC 1950 [http://www.ietf.org/rfc/rfc1952.txt]
   */
  Zlib: v3_CompressionAlgorithm_Zlib,
};
const v3_Confidentiality_Confidentiality: Coding = {
  code: '_Confidentiality',
  display: 'Confidentiality',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_ConfidentialityByAccessKind: Coding = {
  code: '_ConfidentialityByAccessKind',
  display: 'ConfidentialityByAccessKind',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_ConfidentialityByInfoType: Coding = {
  code: '_ConfidentialityByInfoType',
  display: 'ConfidentialityByInfoType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_ConfidentialityModifiers: Coding = {
  code: '_ConfidentialityModifiers',
  display: 'ConfidentialityModifiers',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_Business: Coding = {
  code: 'B',
  display: 'business',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_Celebrity: Coding = {
  code: 'C',
  display: 'celebrity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_Clinician: Coding = {
  code: 'D',
  display: 'clinician',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_SubstanceAbuseRelated: Coding = {
  code: 'ETH',
  display: 'substance abuse related',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_HIVRelated: Coding = {
  code: 'HIV',
  display: 'HIV related',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_Individual: Coding = {
  code: 'I',
  display: 'individual',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_Low: Coding = {
  code: 'L',
  display: 'low',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_Moderate: Coding = {
  code: 'M',
  display: 'moderate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_Normal: Coding = {
  code: 'N',
  display: 'normal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_PsychiatryRelate: Coding = {
  code: 'PSY',
  display: 'psychiatry relate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_Restricted: Coding = {
  code: 'R',
  display: 'restricted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_Sensitive: Coding = {
  code: 'S',
  display: 'sensitive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_SexualAndDomesticViolenceRelated: Coding = {
  code: 'SDV',
  display: 'sexual and domestic violence related',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_Taboo: Coding = {
  code: 'T',
  display: 'taboo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_Unrestricted: Coding = {
  code: 'U',
  display: 'unrestricted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
const v3_Confidentiality_VeryRestricted: Coding = {
  code: 'V',
  display: 'very restricted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
};
/**
 *  A set of codes specifying the security classification of acts and roles in accordance with the definition for concept domain "Confidentiality".
 */
export const V3Confidentiality = {
  /**
   * A specializable code and its leaf codes used in Confidentiality value sets to value the Act.Confidentiality and Role.Confidentiality attribute in accordance with the definition for concept domain "Confidentiality".
   */
  Confidentiality: v3_Confidentiality_Confidentiality,
  /**
   * Description: By accessing subject / role and relationship based  rights  (These concepts are mutually exclusive, one and only one is required for a valid confidentiality coding.)
   *
   *
   *                            Deprecation Comment:Deprecated due to updated confidentiality codes under ActCode
   */
  ConfidentialityByAccessKind: v3_Confidentiality_ConfidentialityByAccessKind,
  /**
   * Description: By information type, only for service catalog entries (multiples allowed). Not to be used with actual patient data!
   *
   *
   *                            Deprecation Comment:Deprecated due to updated confidentiality codes under ActCode
   */
  ConfidentialityByInfoType: v3_Confidentiality_ConfidentialityByInfoType,
  /**
   * Description: Modifiers of role based access rights  (multiple allowed)
   *
   *
   *                            Deprecation Comment:Deprecated due to updated confidentiality codes under ActCode
   */
  ConfidentialityModifiers: v3_Confidentiality_ConfidentialityModifiers,
  /**
   * Description: Since the service class can represent knowledge structures that may be considered a trade or business secret, there is sometimes (though rarely) the need to flag those items as of business level confidentiality.  However, no patient related information may ever be of this confidentiality level.
   *
   *
   *                            Deprecation Comment: Replced by ActCode.B
   */
  Business: v3_Confidentiality_Business,
  /**
   * Description: Celebrities are people of public interest (VIP) including employees, whose information require special protection.
   *
   *
   *                            Deprecation Comment:Replced by ActCode.CEL
   */
  Celebrity: v3_Confidentiality_Celebrity,
  /**
   * Description: Only clinicians may see this item, billing and administration persons can not access this item without special permission.
   *
   *
   *                            Deprecation Comment:Deprecated due to updated confidentiality codes under ActCode
   */
  Clinician: v3_Confidentiality_Clinician,
  /**
   * Description: Alcohol/drug-abuse related item
   *
   *
   *                            Deprecation Comment:Replced by ActCode.ETH
   */
  SubstanceAbuseRelated: v3_Confidentiality_SubstanceAbuseRelated,
  /**
   * Description: HIV and AIDS related item
   *
   *
   *                            Deprecation Comment:Replced by ActCode.HIV
   */
  HIVRelated: v3_Confidentiality_HIVRelated,
  /**
   * Description: Access only to individual persons who are mentioned explicitly as actors of this service and whose actor type warrants that access (cf. to actor type code).
   *
   *
   *                            Deprecation Comment:Deprecated due to updated confidentiality codes under ActCode
   */
  Individual: v3_Confidentiality_Individual,
  /**
   * Definition: Privacy metadata indicating that the information has been de-identified, and there are mitigating circumstances that prevent re-identification, which minimize risk of harm from unauthorized disclosure.  The information requires protection to maintain low sensitivity.
   *
   *
   *                            Examples: Includes anonymized, pseudonymized, or non-personally identifiable information such as HIPAA limited data sets.
   *
   *
   *                            Map: No clear map to ISO 13606-4 Sensitivity Level (1) Care Management:   RECORD_COMPONENTs that might need to be accessed by a wide range of administrative staff to manage the subject of care's access to health services.
   *
   *
   *                            Usage Note: This metadata indicates the receiver may have an obligation to comply with a data use agreement.
   */
  Low: v3_Confidentiality_Low,
  /**
   * Definition: Privacy metadata indicating moderately sensitive information, which presents moderate risk of harm if disclosed without authorization.
   *
   *
   *                            Examples: Includes allergies of non-sensitive nature used inform food service; health information a patient authorizes to be used for marketing, released to a bank for a health credit card or savings account; or information in personal health record systems that are not governed under health privacy laws.
   *
   *
   *                            Map: Partial Map to ISO 13606-4 Sensitivity Level (2) Clinical Management:  Less sensitive RECORD_COMPONENTs that might need to be accessed by a wider range of personnel not all of whom are actively caring for the patient (e.g. radiology staff).
   *
   *
   *                            Usage Note: This metadata indicates that the receiver may be obligated to comply with the receiver's terms of use or privacy policies.
   */
  Moderate: v3_Confidentiality_Moderate,
  /**
   * Definition: Privacy metadata indicating that the information is typical, non-stigmatizing health information, which presents typical risk of harm if disclosed without authorization.
   *
   *
   *                            Examples: In the US, this includes what HIPAA identifies as the minimum necessary protected health information (PHI) given a covered purpose of use (treatment, payment, or operations).  Includes typical, non-stigmatizing health information disclosed in an application for health, workers compensation, disability, or life insurance.
   *
   *
   *                            Map: Partial Map to ISO 13606-4 Sensitivity Level (3) Clinical Care:   Default for normal clinical care access (i.e. most clinical staff directly caring for the patient should be able to access nearly all of the EHR).   Maps to normal confidentiality for treatment information but not to ancillary care, payment and operations.
   *
   *
   *                            Usage Note: This metadata indicates that the receiver may be obligated to comply with applicable jurisdictional privacy law or disclosure authorization.
   */
  Normal: v3_Confidentiality_Normal,
  /**
   * Description: Psychiatry related item
   *
   *
   *                            Deprecation Comment:Replced by ActCode.PSY
   */
  PsychiatryRelate: v3_Confidentiality_PsychiatryRelate,
  /**
   * Privacy metadata indicating highly sensitive, potentially stigmatizing information, which presents a high risk to the information subject if disclosed without authorization. May be pre-empted by jurisdictional law, e.g., for public health reporting or emergency treatment.
   *
   *
   *                            Examples: Includes information that is additionally protected such as sensitive conditions mental health, HIV, substance abuse, domestic violence, child abuse, genetic disease, and reproductive health; or sensitive demographic information such as a patient's standing as an employee or a celebrity. May be used to indicate proprietary or classified information that is not related to an individual, e.g., secret ingredients in a therapeutic substance; or the name of a manufacturer.
   *
   *
   *                            Map: Partial Map to ISO 13606-4 Sensitivity Level (3) Clinical Care: Default for normal clinical care access (i.e. most clinical staff directly caring for the patient should be able to access nearly all of the EHR). Maps to normal confidentiality for treatment information but not to ancillary care, payment and operations..
   *
   *
   *                            Usage Note: This metadata indicates that the receiver may be obligated to comply with applicable, prevailing (default) jurisdictional privacy law or disclosure authorization..
   */
  Restricted: v3_Confidentiality_Restricted,
  /**
   * Description:
   *
   * Information for which the patient seeks heightened confidentiality. Sensitive information is not to be shared with family members.  Information reported by the patient about family members is sensitive by default. Flag can be set or cleared on patient's request.
   *                            Deprecation Comment:Deprecated due to updated confidentiality codes under ActCode
   */
  Sensitive: v3_Confidentiality_Sensitive,
  /**
   * Description: Sexual assault / domestic violence related item
   *
   *
   *                            Deprecation Comment:Replced by ActCode.SDV
   */
  SexualAndDomesticViolenceRelated: v3_Confidentiality_SexualAndDomesticViolenceRelated,
  /**
   * Description: Information not to be disclosed or discussed with patient except through physician assigned to patient in this case.  This is usually a temporary constraint only, example use is a new fatal diagnosis or finding, such as malignancy or HIV.
   *
   *
   *                            Deprecation Note:Replced by ActCode.TBOO
   */
  Taboo: v3_Confidentiality_Taboo,
  /**
   * Definition: Privacy metadata indicating that the information is not classified as sensitive.
   *
   *
   *                            Examples: Includes publicly available information, e.g., business name, phone, email or physical address.
   *
   *
   *                            Usage Note: This metadata indicates that the receiver has no obligation to consider additional policies when making access control decisions.   Note that in some jurisdictions, personally identifiable information must be protected as confidential, so it would not be appropriate to assign a confidentiality code of "unrestricted"  to that information even if it is publicly available.
   */
  Unrestricted: v3_Confidentiality_Unrestricted,
  /**
   * . Privacy metadata indicating that the information is extremely sensitive and likely stigmatizing health information that presents a very high risk if disclosed without authorization.  This information must be kept in the highest confidence.
   *
   *
   *                            Examples:  Includes information about a victim of abuse, patient requested information sensitivity, and taboo subjects relating to health status that must be discussed with the patient by an attending provider before sharing with the patient.  May also include information held under â€œlegal lockâ€? or attorney-client privilege
   *
   *
   *                            Map:  This metadata indicates that the receiver may not disclose this information except as directed by the information custodian, who may be the information subject.
   *
   *
   *                            Usage Note:  This metadata indicates that the receiver may not disclose this information except as directed by the information custodian, who may be the information subject.
   */
  VeryRestricted: v3_Confidentiality_VeryRestricted,
};
/**
 *  Set of codes used to value Act.Confidentiality and Role.Confidentiality attribute in accordance with the definition for concept domain "Confidentiality".
 */
export const V3ConfidentialityClassification = {
  /**
   * Definition: Privacy metadata indicating that the information has been de-identified, and there are mitigating circumstances that prevent re-identification, which minimize risk of harm from unauthorized disclosure.  The information requires protection to maintain low sensitivity.
   *
   *
   *                            Examples: Includes anonymized, pseudonymized, or non-personally identifiable information such as HIPAA limited data sets.
   *
   *
   *                            Map: No clear map to ISO 13606-4 Sensitivity Level (1) Care Management:   RECORD_COMPONENTs that might need to be accessed by a wide range of administrative staff to manage the subject of care's access to health services.
   *
   *
   *                            Usage Note: This metadata indicates the receiver may have an obligation to comply with a data use agreement.
   */
  Low: v3_Confidentiality_Low,
  /**
   * Definition: Privacy metadata indicating moderately sensitive information, which presents moderate risk of harm if disclosed without authorization.
   *
   *
   *                            Examples: Includes allergies of non-sensitive nature used inform food service; health information a patient authorizes to be used for marketing, released to a bank for a health credit card or savings account; or information in personal health record systems that are not governed under health privacy laws.
   *
   *
   *                            Map: Partial Map to ISO 13606-4 Sensitivity Level (2) Clinical Management:  Less sensitive RECORD_COMPONENTs that might need to be accessed by a wider range of personnel not all of whom are actively caring for the patient (e.g. radiology staff).
   *
   *
   *                            Usage Note: This metadata indicates that the receiver may be obligated to comply with the receiver's terms of use or privacy policies.
   */
  Moderate: v3_Confidentiality_Moderate,
  /**
   * Definition: Privacy metadata indicating that the information is typical, non-stigmatizing health information, which presents typical risk of harm if disclosed without authorization.
   *
   *
   *                            Examples: In the US, this includes what HIPAA identifies as the minimum necessary protected health information (PHI) given a covered purpose of use (treatment, payment, or operations).  Includes typical, non-stigmatizing health information disclosed in an application for health, workers compensation, disability, or life insurance.
   *
   *
   *                            Map: Partial Map to ISO 13606-4 Sensitivity Level (3) Clinical Care:   Default for normal clinical care access (i.e. most clinical staff directly caring for the patient should be able to access nearly all of the EHR).   Maps to normal confidentiality for treatment information but not to ancillary care, payment and operations.
   *
   *
   *                            Usage Note: This metadata indicates that the receiver may be obligated to comply with applicable jurisdictional privacy law or disclosure authorization.
   */
  Normal: v3_Confidentiality_Normal,
  /**
   * Privacy metadata indicating highly sensitive, potentially stigmatizing information, which presents a high risk to the information subject if disclosed without authorization. May be pre-empted by jurisdictional law, e.g., for public health reporting or emergency treatment.
   *
   *
   *                            Examples: Includes information that is additionally protected such as sensitive conditions mental health, HIV, substance abuse, domestic violence, child abuse, genetic disease, and reproductive health; or sensitive demographic information such as a patient's standing as an employee or a celebrity. May be used to indicate proprietary or classified information that is not related to an individual, e.g., secret ingredients in a therapeutic substance; or the name of a manufacturer.
   *
   *
   *                            Map: Partial Map to ISO 13606-4 Sensitivity Level (3) Clinical Care: Default for normal clinical care access (i.e. most clinical staff directly caring for the patient should be able to access nearly all of the EHR). Maps to normal confidentiality for treatment information but not to ancillary care, payment and operations..
   *
   *
   *                            Usage Note: This metadata indicates that the receiver may be obligated to comply with applicable, prevailing (default) jurisdictional privacy law or disclosure authorization..
   */
  Restricted: v3_Confidentiality_Restricted,
  /**
   * Definition: Privacy metadata indicating that the information is not classified as sensitive.
   *
   *
   *                            Examples: Includes publicly available information, e.g., business name, phone, email or physical address.
   *
   *
   *                            Usage Note: This metadata indicates that the receiver has no obligation to consider additional policies when making access control decisions.   Note that in some jurisdictions, personally identifiable information must be protected as confidential, so it would not be appropriate to assign a confidentiality code of "unrestricted"  to that information even if it is publicly available.
   */
  Unrestricted: v3_Confidentiality_Unrestricted,
  /**
   * . Privacy metadata indicating that the information is extremely sensitive and likely stigmatizing health information that presents a very high risk if disclosed without authorization.  This information must be kept in the highest confidence.
   *
   *
   *                            Examples:  Includes information about a victim of abuse, patient requested information sensitivity, and taboo subjects relating to health status that must be discussed with the patient by an attending provider before sharing with the patient.  May also include information held under â€œlegal lockâ€? or attorney-client privilege
   *
   *
   *                            Map:  This metadata indicates that the receiver may not disclose this information except as directed by the information custodian, who may be the information subject.
   *
   *
   *                            Usage Note:  This metadata indicates that the receiver may not disclose this information except as directed by the information custodian, who may be the information subject.
   */
  VeryRestricted: v3_Confidentiality_VeryRestricted,
};
const v3_ContainerCap_MedicationCap: Coding = {
  code: '_MedicationCap',
  display: 'MedicationCap',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContainerCap',
};
const v3_ContainerCap_ChildProof: Coding = {
  code: 'CHILD',
  display: 'ChildProof',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContainerCap',
};
const v3_ContainerCap_EasyOpen: Coding = {
  code: 'EASY',
  display: 'EasyOpen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContainerCap',
};
const v3_ContainerCap_Film: Coding = {
  code: 'FILM',
  display: 'Film',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContainerCap',
};
const v3_ContainerCap_Foil: Coding = {
  code: 'FOIL',
  display: 'Foil',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContainerCap',
};
const v3_ContainerCap_PushCap: Coding = {
  code: 'PUSH',
  display: 'Push Cap',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContainerCap',
};
const v3_ContainerCap_ScrewCap: Coding = {
  code: 'SCR',
  display: 'Screw Cap',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContainerCap',
};
/**
 *  The type of cap associated with a container
 */
export const V3ContainerCap = {
  /**
   * Cap types for medication containers
   */
  MedicationCap: v3_ContainerCap_MedicationCap,
  /**
   * A cap designed to be difficult to open for children.  Generally requires multiple simultaneous actions (e.g. squeeze and twist) to open.  Used for products that may be dangerous if ingested or overdosed by children.
   */
  ChildProof: v3_ContainerCap_ChildProof,
  /**
   * A cap designed to be easily removed.  For products intended to be opened by persons with limited strength or dexterity.
   */
  EasyOpen: v3_ContainerCap_EasyOpen,
  /**
   * A non-reactive plastic film covering over the opening of a container.
   */
  Film: v3_ContainerCap_Film,
  /**
   * A foil covering (type of foil not specified) over the opening of a container
   */
  Foil: v3_ContainerCap_Foil,
  /**
   * A non-threaded cap that forms a tight-fitting closure on a container by pushing the fitted end into the conatiner opening
   */
  PushCap: v3_ContainerCap_PushCap,
  /**
   * A threaded cap that is screwed onto the opening of a container
   */
  ScrewCap: v3_ContainerCap_ScrewCap,
};
const v3_ContainerSeparator_Gel: Coding = {
  code: 'GEL',
  display: 'Gel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContainerSeparator',
};
const v3_ContainerSeparator_None: Coding = {
  code: 'NONE',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContainerSeparator',
};
/**
 *  A material in a blood collection container that facilites the separation of of blood cells from serum or plasma
 */
export const V3ContainerSeparator = {
  /**
   * A gelatinous type of separator material.
   */
  Gel: v3_ContainerSeparator_Gel,
  /**
   * No separator material is present in the container.
   */
  None: v3_ContainerSeparator_None,
};
const v3_ContentProcessingMode_Sequential: Coding = {
  code: 'SEQL',
  display: 'Sequential',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContentProcessingMode',
};
const v3_ContentProcessingMode_Unordered: Coding = {
  code: 'UNOR',
  display: 'Unordered',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContentProcessingMode',
};
/**
 *   Description:
 * Identifies the order in which content should be processed.
 */
export const V3ContentProcessingMode = {
  /**
   * Description:The content should be processed in a sequential fashion.
   */
  Sequential: v3_ContentProcessingMode_Sequential,
  /**
   * Description:The content may be processed in any order.
   */
  Unordered: v3_ContentProcessingMode_Unordered,
};
const v3_ContextControl_ContextControlAdditive: Coding = {
  code: '_ContextControlAdditive',
  display: 'ContextControlAdditive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContextControl',
};
const v3_ContextControl_ContextControlNonPropagating: Coding = {
  code: '_ContextControlNonPropagating',
  display: 'ContextControlNonPropagating',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContextControl',
};
const v3_ContextControl_ContextControlOverriding: Coding = {
  code: '_ContextControlOverriding',
  display: 'ContextControlOverriding',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContextControl',
};
const v3_ContextControl_ContextControlPropagating: Coding = {
  code: '_ContextControlPropagating',
  display: 'ContextControlPropagating',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContextControl',
};
const v3_ContextControl_AdditiveNonPropagating: Coding = {
  code: 'AN',
  display: 'additive, non-propagating',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContextControl',
};
const v3_ContextControl_AdditivePropagating: Coding = {
  code: 'AP',
  display: 'additive, propagating',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContextControl',
};
const v3_ContextControl_OverridingNonPropagating: Coding = {
  code: 'ON',
  display: 'overriding, non-propagating',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContextControl',
};
const v3_ContextControl_OverridingPropagating: Coding = {
  code: 'OP',
  display: 'overriding, propagating',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ContextControl',
};
/**
 *  A code that specifies how an ActRelationship or Participation contributes to the context of an Act, and whether it may be propagated to descendent Acts whose association allows such propagation (see also attributes Participation.contextControlCode, ActRelationship.contextControlCode, ActRelationship.contextConductionInd).
 */
export const V3ContextControl = {
  /**
   * The association adds to the existing context associated with the Act.  Both this association and any associations propagated from ancestor Acts are interpreted as being related to this Act.
   */
  ContextControlAdditive: v3_ContextControl_ContextControlAdditive,
  /**
   * The association applies only to the current Act and will not propagate to any child Acts that are related via a conducting ActRelationship (refer to contextConductionInd).
   */
  ContextControlNonPropagating: v3_ContextControl_ContextControlNonPropagating,
  /**
   * The association adds to the existing context associated with the Act, but replaces associations propagated from ancestor Acts whose typeCodes are the same or more specific.
   */
  ContextControlOverriding: v3_ContextControl_ContextControlOverriding,
  /**
   * The association propagates to any child Acts that are related via a conducting ActRelationship (refer to contextConductionInd).
   */
  ContextControlPropagating: v3_ContextControl_ContextControlPropagating,
  /**
   * The association adds to the existing context associated with the Act, but will not propagate to any descendant Acts reached by conducting ActRelationships (see contextControlCode). Examples: If an 'Author' Participation were marked as "Additive, Non-Propagating" it means that the author will be added to the set of author participations that have propagated from ancestor Acts for the purpose of this Act. However only the previously propagated authors will propagate to any child Acts that allow context to be propagated.
   */
  AdditiveNonPropagating: v3_ContextControl_AdditiveNonPropagating,
  /**
   * The association adds to the existing context associated with the Act, and will propagate to any descendant Acts reached by conducting ActRelationships (see contextControlCode). Examples: If an 'Author' Participation were marked as "Additive, Propagating" it means that the author will be added to the set of author participations that have propagated from ancestor Acts, and will itself propagate with the other authors to any child Acts that allow context to be propagated.
   */
  AdditivePropagating: v3_ContextControl_AdditivePropagating,
  /**
   * The association is added to the existing context associated with the Act, but overrides an association with the same typeCode. However, this overriding association will not propagate to any descendant Acts reached by conducting ActRelationships (see contextControlCode). Examples: If an 'Author' Participation were marked as "Overriding, Non-Propagating" it means that the author will replace the set of author participations that have propagated from ancestor Acts. Furthermore, no author participations whatsoever will propagate to any child Acts that allow context to be propagated.
   */
  OverridingNonPropagating: v3_ContextControl_OverridingNonPropagating,
  /**
   * The association is added to the existing context associated with the Act, but overrides an association with the same typeCode. This overriding association will propagate to any descendant Acts reached by conducting ActRelationships (see contextControlCode). Examples: If an 'Author' Participation were marked as "Overriding, Propagating" it means that the author will replace the set of author participations that have propagated from ancestor Acts, and will itself be the only author to propagate to any child Acts that allow context to be propagated.
   */
  OverridingPropagating: v3_ContextControl_OverridingPropagating,
};
const v3_DataOperation_Execute: Coding = {
  code: 'EXECUTE',
  display: 'execute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_DataOperation_Jump: Coding = {
  code: 'JUMP',
  display: 'jump',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_DataOperation_ModifyStatus: Coding = {
  code: 'MODIFYSTATUS',
  display: 'modify status',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_DataOperation_Operate: Coding = {
  code: 'OPERATE',
  display: 'operate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
const v3_DataOperation_Read: Coding = {
  code: 'READ',
  display: 'read',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3DataOperation = {
  /**
   * Description:Change the status of an object representing an Act to "aborted", i.e., terminated prior to the originally intended completion. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Abort: v3_DataOperation_Abort,
  /**
   * Description:Change the status of an object representing an Act to "active", i.e., so it can be performed or is being performed, for the first time. (Contrast with REACTIVATE.) For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Activate: v3_DataOperation_Activate,
  /**
   * Description:Fundamental operation in an Information System (IS) that results only in the addition of information to an object already in existence. Note: The preceding definition is taken from the HL7 RBAC specification.  There is no restriction on how the operation is invoked, e.g., via a user interface.
   */
  Append: v3_DataOperation_Append,
  /**
   * Description:Change the status of an object representing an Act to "cancelled", i.e., abandoned before activation. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Cancel: v3_DataOperation_Cancel,
  /**
   * Description:Change the status of an object representing an Act to "completed", i.e., terminated normally after all of its constituents have been performed. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Complete: v3_DataOperation_Complete,
  /**
   * Description:Fundamental operation in an Information System (IS) that results only in the act of bringing an object into existence. Note: The preceding definition is taken from the HL7 RBAC specification.  There is no restriction on how the operation is invoked, e.g., via a user interface. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Create: v3_DataOperation_Create,
  /**
   * Description:Fundamental operation in an Information System (IS) that results only in the removal of information about an object from memory or storage. Note: The preceding definition is taken from the HL7 RBAC specification.  There is no restriction on how the operation is invoked, e.g., via a user interface.
   */
  Delete: v3_DataOperation_Delete,
  /**
   * Description:Fundamental operation in an IS that results only in initiating performance of a single or set of programs (i.e., software objects). Note: The preceding definition is taken from the HL7 RBAC specification.  There is no restriction on how the operation is invoked, e.g., via a user interface.
   */
  Execute: v3_DataOperation_Execute,
  /**
   * Description:Change the status of an object representing an Act to "held", i.e., put aside an Act that is still in preparatory stages.  No action can occur until the Act is released. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Hold: v3_DataOperation_Hold,
  /**
   * Description:Change the status of an object representing an Act to a normal state. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Jump: v3_DataOperation_Jump,
  /**
   * Description:Change the status of an object representing an Act.
   */
  ModifyStatus: v3_DataOperation_ModifyStatus,
  /**
   * Description:Change the status of an object representing an Act to "nullified", i.e., treat as though it never existed. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Nullify: v3_DataOperation_Nullify,
  /**
   * Description:Change the status of an object representing an Act to "obsolete" when it has been replaced by a new instance. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Obsolete: v3_DataOperation_Obsolete,
  /**
   * Description:Act on an object or objects.
   */
  Operate: v3_DataOperation_Operate,
  /**
   * Description:Change the status of a formerly active object representing an Act to "active", i.e., so it can again be performed or is being performed. (Contrast with ACTIVATE.) For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Reactivate: v3_DataOperation_Reactivate,
  /**
   * Description:Fundamental operation in an Information System (IS) that results only in the flow of information about an object to a subject. Note: The preceding definition is taken from the HL7 RBAC specification.  There is no restriction on how the operation is invoked, e.g., via a user interface.
   */
  Read: v3_DataOperation_Read,
  /**
   * Description:Change the status of an object representing an Act so it is no longer "held", i.e., allow action to occur. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Release: v3_DataOperation_Release,
  /**
   * Description:Change the status of a suspended object representing an Act to "active", i.e., so it can be performed or is being performed. For an HL7 Act, the state transitions per the HL7 Reference Information Model.
   */
  Resume: v3_DataOperation_Resume,
  /**
   * Definition:Change the status of an object representing an Act to suspended, i.e., so it is temporarily not in service.
   */
  Suspend: v3_DataOperation_Suspend,
  /**
   * Definition:Fundamental operation in an Information System (IS) that results only in the revision or alteration of an object. Note: The preceding definition is taken from the HL7 RBAC specification. There is no restriction on how the operation is invoked, e.g., via a user interface.
   */
  Revise: v3_DataOperation_Revise,
};
const v3_Dentition_ArtificialDentition: Coding = {
  code: 'ArtificialDentition',
  display: 'Artificial dentition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_PermanentDentition: Coding = {
  code: 'PermanentDentition',
  display: 'Permanent dentition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_PrimaryDentition: Coding = {
  code: 'PrimaryDentition',
  display: 'Primary dentition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_SupernumeraryTooth: Coding = {
  code: 'SupernumeraryTooth',
  display: 'Supernumerary Tooth',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL1: Coding = {
  code: 'TID1',
  display: '1',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL10: Coding = {
  code: 'TID10',
  display: '10',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL10a: Coding = {
  code: 'TID10a',
  display: '10a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL10i: Coding = {
  code: 'TID10i',
  display: '10i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL10p: Coding = {
  code: 'TID10p',
  display: '10p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL10pd: Coding = {
  code: 'TID10pd',
  display: '10pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL10pm: Coding = {
  code: 'TID10pm',
  display: '10pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL10s: Coding = {
  code: 'TID10s',
  display: '10s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL11: Coding = {
  code: 'TID11',
  display: '11',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL11a: Coding = {
  code: 'TID11a',
  display: '11a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL11i: Coding = {
  code: 'TID11i',
  display: '11i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL11p: Coding = {
  code: 'TID11p',
  display: '11p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL11pd: Coding = {
  code: 'TID11pd',
  display: '11pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL11pm: Coding = {
  code: 'TID11pm',
  display: '11pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL11s: Coding = {
  code: 'TID11s',
  display: '11s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL12: Coding = {
  code: 'TID12',
  display: '12',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL12a: Coding = {
  code: 'TID12a',
  display: '12a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL12i: Coding = {
  code: 'TID12i',
  display: '12i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL12p: Coding = {
  code: 'TID12p',
  display: '12p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL12pd: Coding = {
  code: 'TID12pd',
  display: '12pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL12pm: Coding = {
  code: 'TID12pm',
  display: '12pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL12s: Coding = {
  code: 'TID12s',
  display: '12s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL13: Coding = {
  code: 'TID13',
  display: '13',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL13a: Coding = {
  code: 'TID13a',
  display: '13a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL13i: Coding = {
  code: 'TID13i',
  display: '13i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL13p: Coding = {
  code: 'TID13p',
  display: '13p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL13pd: Coding = {
  code: 'TID13pd',
  display: '13pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL13pm: Coding = {
  code: 'TID13pm',
  display: '13pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL13s: Coding = {
  code: 'TID13s',
  display: '13s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL14: Coding = {
  code: 'TID14',
  display: '14',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL14a: Coding = {
  code: 'TID14a',
  display: '14a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL14i: Coding = {
  code: 'TID14i',
  display: '14i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL14p: Coding = {
  code: 'TID14p',
  display: '14p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL14pd: Coding = {
  code: 'TID14pd',
  display: '14pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL14pm: Coding = {
  code: 'TID14pm',
  display: '14pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL14s: Coding = {
  code: 'TID14s',
  display: '14s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL15: Coding = {
  code: 'TID15',
  display: '15',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL15a: Coding = {
  code: 'TID15a',
  display: '15a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL15i: Coding = {
  code: 'TID15i',
  display: '15i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL15p: Coding = {
  code: 'TID15p',
  display: '15p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL15pd: Coding = {
  code: 'TID15pd',
  display: '15pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL15pm: Coding = {
  code: 'TID15pm',
  display: '15pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL15s: Coding = {
  code: 'TID15s',
  display: '15s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL16: Coding = {
  code: 'TID16',
  display: '16',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL16a: Coding = {
  code: 'TID16a',
  display: '16a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL16i: Coding = {
  code: 'TID16i',
  display: '16i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL16p: Coding = {
  code: 'TID16p',
  display: '16p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL16pd: Coding = {
  code: 'TID16pd',
  display: '16pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL16pm: Coding = {
  code: 'TID16pm',
  display: '16pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL16s: Coding = {
  code: 'TID16s',
  display: '16s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL17: Coding = {
  code: 'TID17',
  display: '17',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL17a: Coding = {
  code: 'TID17a',
  display: '17a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL17ad: Coding = {
  code: 'TID17ad',
  display: '17ad',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL17am: Coding = {
  code: 'TID17am',
  display: '17am',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL17d: Coding = {
  code: 'TID17d',
  display: '17d',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL17i: Coding = {
  code: 'TID17i',
  display: '17i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL17id: Coding = {
  code: 'TID17id',
  display: '17id',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL17im: Coding = {
  code: 'TID17im',
  display: '17im',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL17m: Coding = {
  code: 'TID17m',
  display: '17m',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL17p: Coding = {
  code: 'TID17p',
  display: '17p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL17pd: Coding = {
  code: 'TID17pd',
  display: '17pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL17pm: Coding = {
  code: 'TID17pm',
  display: '17pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL17s: Coding = {
  code: 'TID17s',
  display: '17s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL18: Coding = {
  code: 'TID18',
  display: '18',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL18a: Coding = {
  code: 'TID18a',
  display: '18a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL18ad: Coding = {
  code: 'TID18ad',
  display: '18ad',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL18am: Coding = {
  code: 'TID18am',
  display: '18am',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL18d: Coding = {
  code: 'TID18d',
  display: '18d',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL18i: Coding = {
  code: 'TID18i',
  display: '18i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL18id: Coding = {
  code: 'TID18id',
  display: '18id',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL18im: Coding = {
  code: 'TID18im',
  display: '18im',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL18m: Coding = {
  code: 'TID18m',
  display: '18m',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL18p: Coding = {
  code: 'TID18p',
  display: '18p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL18pd: Coding = {
  code: 'TID18pd',
  display: '18pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL18pm: Coding = {
  code: 'TID18pm',
  display: '18pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL18s: Coding = {
  code: 'TID18s',
  display: '18s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL19: Coding = {
  code: 'TID19',
  display: '19',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL19a: Coding = {
  code: 'TID19a',
  display: '19a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL19ad: Coding = {
  code: 'TID19ad',
  display: '19ad',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL19am: Coding = {
  code: 'TID19am',
  display: '19am',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL19d: Coding = {
  code: 'TID19d',
  display: '19d',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL19i: Coding = {
  code: 'TID19i',
  display: '19i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL19id: Coding = {
  code: 'TID19id',
  display: '19id',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL19im: Coding = {
  code: 'TID19im',
  display: '19im',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL19m: Coding = {
  code: 'TID19m',
  display: '19m',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL19p: Coding = {
  code: 'TID19p',
  display: '19p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL19pd: Coding = {
  code: 'TID19pd',
  display: '19pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL19pm: Coding = {
  code: 'TID19pm',
  display: '19pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL19s: Coding = {
  code: 'TID19s',
  display: '19s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL1a: Coding = {
  code: 'TID1a',
  display: '1a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL1i: Coding = {
  code: 'TID1i',
  display: '1i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL1p: Coding = {
  code: 'TID1p',
  display: '1p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL1pd: Coding = {
  code: 'TID1pd',
  display: '1pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL1pm: Coding = {
  code: 'TID1pm',
  display: '1pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL1s: Coding = {
  code: 'TID1s',
  display: '1s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL2: Coding = {
  code: 'TID2',
  display: '2',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL20: Coding = {
  code: 'TID20',
  display: '20',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL20a: Coding = {
  code: 'TID20a',
  display: '20a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL20i: Coding = {
  code: 'TID20i',
  display: '20i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL20p: Coding = {
  code: 'TID20p',
  display: '20p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL20pd: Coding = {
  code: 'TID20pd',
  display: '20pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL20pm: Coding = {
  code: 'TID20pm',
  display: '20pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL20s: Coding = {
  code: 'TID20s',
  display: '20s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL21: Coding = {
  code: 'TID21',
  display: '21',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL21a: Coding = {
  code: 'TID21a',
  display: '21a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL21i: Coding = {
  code: 'TID21i',
  display: '21i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL21p: Coding = {
  code: 'TID21p',
  display: '21p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL21pd: Coding = {
  code: 'TID21pd',
  display: '21pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL21pm: Coding = {
  code: 'TID21pm',
  display: '21pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL21s: Coding = {
  code: 'TID21s',
  display: '21s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL22: Coding = {
  code: 'TID22',
  display: '22',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL22a: Coding = {
  code: 'TID22a',
  display: '22a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL22i: Coding = {
  code: 'TID22i',
  display: '22i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL22p: Coding = {
  code: 'TID22p',
  display: '22p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL22pd: Coding = {
  code: 'TID22pd',
  display: '22pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL22pm: Coding = {
  code: 'TID22pm',
  display: '22pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL22s: Coding = {
  code: 'TID22s',
  display: '22s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL23: Coding = {
  code: 'TID23',
  display: '23',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL23a: Coding = {
  code: 'TID23a',
  display: '23a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL23i: Coding = {
  code: 'TID23i',
  display: '23i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL23p: Coding = {
  code: 'TID23p',
  display: '23p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL23pd: Coding = {
  code: 'TID23pd',
  display: '23pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL23pm: Coding = {
  code: 'TID23pm',
  display: '23pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL23s: Coding = {
  code: 'TID23s',
  display: '23s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL24: Coding = {
  code: 'TID24',
  display: '24',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL24a: Coding = {
  code: 'TID24a',
  display: '24a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL24i: Coding = {
  code: 'TID24i',
  display: '24i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL24p: Coding = {
  code: 'TID24p',
  display: '24p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL24pd: Coding = {
  code: 'TID24pd',
  display: '24pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL24pm: Coding = {
  code: 'TID24pm',
  display: '24pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL24s: Coding = {
  code: 'TID24s',
  display: '24s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL25: Coding = {
  code: 'TID25',
  display: '25',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL25a: Coding = {
  code: 'TID25a',
  display: '25a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL25i: Coding = {
  code: 'TID25i',
  display: '25i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL25p: Coding = {
  code: 'TID25p',
  display: '25p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL25pd: Coding = {
  code: 'TID25pd',
  display: '25pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL25pm: Coding = {
  code: 'TID25pm',
  display: '25pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL25s: Coding = {
  code: 'TID25s',
  display: '25s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL26: Coding = {
  code: 'TID26',
  display: '26',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL26a: Coding = {
  code: 'TID26a',
  display: '26a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL26i: Coding = {
  code: 'TID26i',
  display: '26i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL26p: Coding = {
  code: 'TID26p',
  display: '26p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL26pd: Coding = {
  code: 'TID26pd',
  display: '26pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL26pm: Coding = {
  code: 'TID26pm',
  display: '26pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL26s: Coding = {
  code: 'TID26s',
  display: '26s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL27: Coding = {
  code: 'TID27',
  display: '27',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL27a: Coding = {
  code: 'TID27a',
  display: '27a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL27i: Coding = {
  code: 'TID27i',
  display: '27i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL27p: Coding = {
  code: 'TID27p',
  display: '27p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL27pd: Coding = {
  code: 'TID27pd',
  display: '27pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL27pm: Coding = {
  code: 'TID27pm',
  display: '27pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL27s: Coding = {
  code: 'TID27s',
  display: '27s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL28: Coding = {
  code: 'TID28',
  display: '28',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL28a: Coding = {
  code: 'TID28a',
  display: '28a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL28i: Coding = {
  code: 'TID28i',
  display: '28i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL28p: Coding = {
  code: 'TID28p',
  display: '28p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL28pd: Coding = {
  code: 'TID28pd',
  display: '28pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL28pm: Coding = {
  code: 'TID28pm',
  display: '28pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL28s: Coding = {
  code: 'TID28s',
  display: '28s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL29: Coding = {
  code: 'TID29',
  display: '29',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL29a: Coding = {
  code: 'TID29a',
  display: '29a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL29i: Coding = {
  code: 'TID29i',
  display: '29i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL29p: Coding = {
  code: 'TID29p',
  display: '29p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL29pd: Coding = {
  code: 'TID29pd',
  display: '29pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL29pm: Coding = {
  code: 'TID29pm',
  display: '29pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL29s: Coding = {
  code: 'TID29s',
  display: '29s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL2a: Coding = {
  code: 'TID2a',
  display: '2a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL2i: Coding = {
  code: 'TID2i',
  display: '2i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL2p: Coding = {
  code: 'TID2p',
  display: '2p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL2pd: Coding = {
  code: 'TID2pd',
  display: '2pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL2pm: Coding = {
  code: 'TID2pm',
  display: '2pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL2s: Coding = {
  code: 'TID2s',
  display: '2s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL3: Coding = {
  code: 'TID3',
  display: '3',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL30: Coding = {
  code: 'TID30',
  display: '30',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL30a: Coding = {
  code: 'TID30a',
  display: '30a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL30ad: Coding = {
  code: 'TID30ad',
  display: '30ad',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL30am: Coding = {
  code: 'TID30am',
  display: '30am',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL30d: Coding = {
  code: 'TID30d',
  display: '30d',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL30i: Coding = {
  code: 'TID30i',
  display: '30i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL30id: Coding = {
  code: 'TID30id',
  display: '30id',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL30im: Coding = {
  code: 'TID30im',
  display: '30im',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL30m: Coding = {
  code: 'TID30m',
  display: '30m',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL30p: Coding = {
  code: 'TID30p',
  display: '30p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL30pd: Coding = {
  code: 'TID30pd',
  display: '30pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL30pm: Coding = {
  code: 'TID30pm',
  display: '30pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL30s: Coding = {
  code: 'TID30s',
  display: '30s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL31: Coding = {
  code: 'TID31',
  display: '31',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL31a: Coding = {
  code: 'TID31a',
  display: '31a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL31ad: Coding = {
  code: 'TID31ad',
  display: '31ad',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL31am: Coding = {
  code: 'TID31am',
  display: '31am',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL31d: Coding = {
  code: 'TID31d',
  display: '31d',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL31i: Coding = {
  code: 'TID31i',
  display: '31i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL31id: Coding = {
  code: 'TID31id',
  display: '31id',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL31im: Coding = {
  code: 'TID31im',
  display: '31im',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL31m: Coding = {
  code: 'TID31m',
  display: '31m',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL31p: Coding = {
  code: 'TID31p',
  display: '31p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL31pd: Coding = {
  code: 'TID31pd',
  display: '31pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL31pm: Coding = {
  code: 'TID31pm',
  display: '31pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL31s: Coding = {
  code: 'TID31s',
  display: '31s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL32: Coding = {
  code: 'TID32',
  display: '32',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL32a: Coding = {
  code: 'TID32a',
  display: '32a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL32ad: Coding = {
  code: 'TID32ad',
  display: '32ad',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL32am: Coding = {
  code: 'TID32am',
  display: '32am',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL32d: Coding = {
  code: 'TID32d',
  display: '32d',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL32i: Coding = {
  code: 'TID32i',
  display: '32i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL32id: Coding = {
  code: 'TID32id',
  display: '32id',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL32im: Coding = {
  code: 'TID32im',
  display: '32im',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL32m: Coding = {
  code: 'TID32m',
  display: '32m',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL32p: Coding = {
  code: 'TID32p',
  display: '32p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL32pd: Coding = {
  code: 'TID32pd',
  display: '32pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL32pm: Coding = {
  code: 'TID32pm',
  display: '32pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL32s: Coding = {
  code: 'TID32s',
  display: '32s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL3a: Coding = {
  code: 'TID3a',
  display: '3a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL3i: Coding = {
  code: 'TID3i',
  display: '3i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL3p: Coding = {
  code: 'TID3p',
  display: '3p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL3pd: Coding = {
  code: 'TID3pd',
  display: '3pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL3pm: Coding = {
  code: 'TID3pm',
  display: '3pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL3s: Coding = {
  code: 'TID3s',
  display: '3s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL4: Coding = {
  code: 'TID4',
  display: '4',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL4a: Coding = {
  code: 'TID4a',
  display: '4a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL4i: Coding = {
  code: 'TID4i',
  display: '4i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL4p: Coding = {
  code: 'TID4p',
  display: '4p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL4pd: Coding = {
  code: 'TID4pd',
  display: '4pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL4pm: Coding = {
  code: 'TID4pm',
  display: '4pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL4s: Coding = {
  code: 'TID4s',
  display: '4s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL5: Coding = {
  code: 'TID5',
  display: '5',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL5a: Coding = {
  code: 'TID5a',
  display: '5a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL5i: Coding = {
  code: 'TID5i',
  display: '5i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL5p: Coding = {
  code: 'TID5p',
  display: '5p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL5pd: Coding = {
  code: 'TID5pd',
  display: '5pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL5pm: Coding = {
  code: 'TID5pm',
  display: '5pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL5s: Coding = {
  code: 'TID5s',
  display: '5s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL6: Coding = {
  code: 'TID6',
  display: '6',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL6a: Coding = {
  code: 'TID6a',
  display: '6a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL6i: Coding = {
  code: 'TID6i',
  display: '6i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL6p: Coding = {
  code: 'TID6p',
  display: '6p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL6pd: Coding = {
  code: 'TID6pd',
  display: '6pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL6pm: Coding = {
  code: 'TID6pm',
  display: '6pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL6s: Coding = {
  code: 'TID6s',
  display: '6s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL7: Coding = {
  code: 'TID7',
  display: '7',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL7a: Coding = {
  code: 'TID7a',
  display: '7a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL7i: Coding = {
  code: 'TID7i',
  display: '7i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL7p: Coding = {
  code: 'TID7p',
  display: '7p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL7pd: Coding = {
  code: 'TID7pd',
  display: '7pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL7pm: Coding = {
  code: 'TID7pm',
  display: '7pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL7s: Coding = {
  code: 'TID7s',
  display: '7s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL8: Coding = {
  code: 'TID8',
  display: '8',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL8a: Coding = {
  code: 'TID8a',
  display: '8a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL8i: Coding = {
  code: 'TID8i',
  display: '8i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL8p: Coding = {
  code: 'TID8p',
  display: '8p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL8pd: Coding = {
  code: 'TID8pd',
  display: '8pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL8pm: Coding = {
  code: 'TID8pm',
  display: '8pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL8s: Coding = {
  code: 'TID8s',
  display: '8s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL9: Coding = {
  code: 'TID9',
  display: '9',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL9a: Coding = {
  code: 'TID9a',
  display: '9a',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL9i: Coding = {
  code: 'TID9i',
  display: '9i',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL9p: Coding = {
  code: 'TID9p',
  display: '9p',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL9pd: Coding = {
  code: 'TID9pd',
  display: '9pd',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL9pm: Coding = {
  code: 'TID9pm',
  display: '9pm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_VAL9s: Coding = {
  code: 'TID9s',
  display: '9s',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_A: Coding = {
  code: 'TIDA',
  display: 'A',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_As: Coding = {
  code: 'TIDAs',
  display: 'As',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_B: Coding = {
  code: 'TIDB',
  display: 'B',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Bs: Coding = {
  code: 'TIDBs',
  display: 'Bs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_C: Coding = {
  code: 'TIDC',
  display: 'C',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Cs: Coding = {
  code: 'TIDCs',
  display: 'Cs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_D: Coding = {
  code: 'TIDD',
  display: 'D',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Ds: Coding = {
  code: 'TIDDs',
  display: 'Ds',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_E: Coding = {
  code: 'TIDE',
  display: 'E',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Es: Coding = {
  code: 'TIDEs',
  display: 'Es',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_F: Coding = {
  code: 'TIDF',
  display: 'F',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Fs: Coding = {
  code: 'TIDFs',
  display: 'Fs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_G: Coding = {
  code: 'TIDG',
  display: 'G',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Gs: Coding = {
  code: 'TIDGs',
  display: 'Gs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_H: Coding = {
  code: 'TIDH',
  display: 'H',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Hs: Coding = {
  code: 'TIDHs',
  display: 'Hs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_I: Coding = {
  code: 'TIDI',
  display: 'I',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Is: Coding = {
  code: 'TIDIs',
  display: 'Is',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_J: Coding = {
  code: 'TIDJ',
  display: 'J',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Js: Coding = {
  code: 'TIDJs',
  display: 'Js',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_K: Coding = {
  code: 'TIDK',
  display: 'K',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Ks: Coding = {
  code: 'TIDKs',
  display: 'Ks',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_L: Coding = {
  code: 'TIDL',
  display: 'L',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Ls: Coding = {
  code: 'TIDLs',
  display: 'Ls',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_M: Coding = {
  code: 'TIDM',
  display: 'M',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Ms: Coding = {
  code: 'TIDMs',
  display: 'Ms',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_N: Coding = {
  code: 'TIDN',
  display: 'N',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Ns: Coding = {
  code: 'TIDNs',
  display: 'Ns',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_O: Coding = {
  code: 'TIDO',
  display: 'O',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Os: Coding = {
  code: 'TIDOs',
  display: 'Os',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_P: Coding = {
  code: 'TIDP',
  display: 'P',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Ps: Coding = {
  code: 'TIDPs',
  display: 'Ps',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Q: Coding = {
  code: 'TIDQ',
  display: 'Q',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Qs: Coding = {
  code: 'TIDQs',
  display: 'Qs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_R: Coding = {
  code: 'TIDR',
  display: 'R',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Rs: Coding = {
  code: 'TIDRs',
  display: 'Rs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_S: Coding = {
  code: 'TIDS',
  display: 'S',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Ss: Coding = {
  code: 'TIDSs',
  display: 'Ss',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_T: Coding = {
  code: 'TIDT',
  display: 'T',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
const v3_Dentition_Ts: Coding = {
  code: 'TIDTs',
  display: 'Ts',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Dentition',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3Dentition = {
  /**
   * Artificial dentition, artificial subsitutes for the natural dentition
   */
  ArtificialDentition: v3_Dentition_ArtificialDentition,
  /**
   * Permanent dentition, the natural teeth of adulthood that replace or are added to the deciduous teeth
   */
  PermanentDentition: v3_Dentition_PermanentDentition,
  /**
   * Primary dentition, the first teeth to errupt and usually replaced with permanent dentition
   */
  PrimaryDentition: v3_Dentition_PrimaryDentition,
  /**
   * Supernumerary tooth, any tooth in addition to the normal permanent and primary dentition
   */
  SupernumeraryTooth: v3_Dentition_SupernumeraryTooth,
  /**
   * Maxillary right third molar
   */
  VAL1: v3_Dentition_VAL1,
  /**
   * Maxillary left lateral incisor
   */
  VAL10: v3_Dentition_VAL10,
  /**
   * Maxillary left lateral incisor abutment
   */
  VAL10a: v3_Dentition_VAL10a,
  /**
   * Maxillary left lateral incisor implant
   */
  VAL10i: v3_Dentition_VAL10i,
  /**
   * Maxillary left lateral incisor prosthesis
   */
  VAL10p: v3_Dentition_VAL10p,
  /**
   * Maxillary left lateral incisor distal prosthesis
   */
  VAL10pd: v3_Dentition_VAL10pd,
  /**
   * Maxillary left lateral incisor mesial prosthesis
   */
  VAL10pm: v3_Dentition_VAL10pm,
  /**
   * Supernumerary maxillary left lateral incisor
   */
  VAL10s: v3_Dentition_VAL10s,
  /**
   * Maxillary left canine
   */
  VAL11: v3_Dentition_VAL11,
  /**
   * Maxillary left canine abutment
   */
  VAL11a: v3_Dentition_VAL11a,
  /**
   * Maxillary left canine implant
   */
  VAL11i: v3_Dentition_VAL11i,
  /**
   * Maxillary left canine prosthesis
   */
  VAL11p: v3_Dentition_VAL11p,
  /**
   * Maxillary left canine distal prosthesis
   */
  VAL11pd: v3_Dentition_VAL11pd,
  /**
   * Maxillary left canine mesial prosthesis
   */
  VAL11pm: v3_Dentition_VAL11pm,
  /**
   * Supernumerary maxillary left canine
   */
  VAL11s: v3_Dentition_VAL11s,
  /**
   * Maxillary left first premolar
   */
  VAL12: v3_Dentition_VAL12,
  /**
   * Maxillary left first premolar abutment
   */
  VAL12a: v3_Dentition_VAL12a,
  /**
   * Maxillary left first premolar implant
   */
  VAL12i: v3_Dentition_VAL12i,
  /**
   * Maxillary left first premolar prosthesis
   */
  VAL12p: v3_Dentition_VAL12p,
  /**
   * Maxillary left first premolar distal prosthesis
   */
  VAL12pd: v3_Dentition_VAL12pd,
  /**
   * Maxillary left first premolar mesial prosthesis
   */
  VAL12pm: v3_Dentition_VAL12pm,
  /**
   * Supernumerary maxillary left first premolar
   */
  VAL12s: v3_Dentition_VAL12s,
  /**
   * Maxillary left second premolar
   */
  VAL13: v3_Dentition_VAL13,
  /**
   * Maxillary left second premolar abutment
   */
  VAL13a: v3_Dentition_VAL13a,
  /**
   * Maxillary left second premolar implant
   */
  VAL13i: v3_Dentition_VAL13i,
  /**
   * Maxillary left second premolar prosthesis
   */
  VAL13p: v3_Dentition_VAL13p,
  /**
   * Maxillary left second premolar distal prosthesis
   */
  VAL13pd: v3_Dentition_VAL13pd,
  /**
   * Maxillary left second premolar mesial prosthesis
   */
  VAL13pm: v3_Dentition_VAL13pm,
  /**
   * Supernumerary maxillary left second premolar
   */
  VAL13s: v3_Dentition_VAL13s,
  /**
   * Maxillary left first molar
   */
  VAL14: v3_Dentition_VAL14,
  /**
   * Maxillary left first molar abutment
   */
  VAL14a: v3_Dentition_VAL14a,
  /**
   * Maxillary left first molar implant
   */
  VAL14i: v3_Dentition_VAL14i,
  /**
   * Maxillary left first molar prosthesis
   */
  VAL14p: v3_Dentition_VAL14p,
  /**
   * Maxillary left first molar distal prosthesis
   */
  VAL14pd: v3_Dentition_VAL14pd,
  /**
   * Maxillary left first molar mesial prosthesis
   */
  VAL14pm: v3_Dentition_VAL14pm,
  /**
   * Supernumerary maxillary left first molar
   */
  VAL14s: v3_Dentition_VAL14s,
  /**
   * Maxillary left second molar
   */
  VAL15: v3_Dentition_VAL15,
  /**
   * Maxillary left second molar abutment
   */
  VAL15a: v3_Dentition_VAL15a,
  /**
   * Maxillary left second molar implant
   */
  VAL15i: v3_Dentition_VAL15i,
  /**
   * Maxillary left second molar prosthesis
   */
  VAL15p: v3_Dentition_VAL15p,
  /**
   * Maxillary left second molar distal prosthesis
   */
  VAL15pd: v3_Dentition_VAL15pd,
  /**
   * Maxillary left second molar mesial prosthesis
   */
  VAL15pm: v3_Dentition_VAL15pm,
  /**
   * Supernumerary maxillary left second molar
   */
  VAL15s: v3_Dentition_VAL15s,
  /**
   * Maxillary left third molar
   */
  VAL16: v3_Dentition_VAL16,
  /**
   * Maxillary left third molar abutment
   */
  VAL16a: v3_Dentition_VAL16a,
  /**
   * Maxillary left third molar implant
   */
  VAL16i: v3_Dentition_VAL16i,
  /**
   * Maxillary left third molar prosthesis
   */
  VAL16p: v3_Dentition_VAL16p,
  /**
   * Maxillary left third molar distal prosthesis
   */
  VAL16pd: v3_Dentition_VAL16pd,
  /**
   * Maxillary left third molar mesial prosthesis
   */
  VAL16pm: v3_Dentition_VAL16pm,
  /**
   * Supernumerary maxillary left third molar
   */
  VAL16s: v3_Dentition_VAL16s,
  /**
   * Mandibular left third molar
   */
  VAL17: v3_Dentition_VAL17,
  /**
   * Mandibular left third molar abutment
   */
  VAL17a: v3_Dentition_VAL17a,
  /**
   * Mandibular left third molar abutment distal hemisection
   */
  VAL17ad: v3_Dentition_VAL17ad,
  /**
   * Mandibular left third molar abutment mesial hemisection
   */
  VAL17am: v3_Dentition_VAL17am,
  /**
   * Mandibular left third molar distal hemisection
   */
  VAL17d: v3_Dentition_VAL17d,
  /**
   * Mandibular left third molar implant
   */
  VAL17i: v3_Dentition_VAL17i,
  /**
   * Mandibular left third molar implant distal hemisection
   */
  VAL17id: v3_Dentition_VAL17id,
  /**
   * Mandibular left third molar implant mesial hemisection
   */
  VAL17im: v3_Dentition_VAL17im,
  /**
   * Mandibular left third molar mesial hemisection
   */
  VAL17m: v3_Dentition_VAL17m,
  /**
   * Mandibular left third molar prosthesis
   */
  VAL17p: v3_Dentition_VAL17p,
  /**
   * Mandibular left third molar distal prosthesis
   */
  VAL17pd: v3_Dentition_VAL17pd,
  /**
   * Mandibular left third molar mesial prosthesis
   */
  VAL17pm: v3_Dentition_VAL17pm,
  /**
   * Supernumerary mandibular left third molar
   */
  VAL17s: v3_Dentition_VAL17s,
  /**
   * Mandibular left second molar
   */
  VAL18: v3_Dentition_VAL18,
  /**
   * Mandibular left second molar abutment
   */
  VAL18a: v3_Dentition_VAL18a,
  /**
   * Mandibular left second molar abutment distal hemisection
   */
  VAL18ad: v3_Dentition_VAL18ad,
  /**
   * Mandibular left second molar abutment mesial hemisection
   */
  VAL18am: v3_Dentition_VAL18am,
  /**
   * Mandibular left second molar distal hemisection
   */
  VAL18d: v3_Dentition_VAL18d,
  /**
   * Mandibular left second molar implant
   */
  VAL18i: v3_Dentition_VAL18i,
  /**
   * Mandibular left second molar implant distal hemisection
   */
  VAL18id: v3_Dentition_VAL18id,
  /**
   * Mandibular left second molar implant mesial hemisection
   */
  VAL18im: v3_Dentition_VAL18im,
  /**
   * Mandibular left second molar mesial hemisection
   */
  VAL18m: v3_Dentition_VAL18m,
  /**
   * Mandibular left second molar prosthesis
   */
  VAL18p: v3_Dentition_VAL18p,
  /**
   * Mandibular left second molar distal prosthesis
   */
  VAL18pd: v3_Dentition_VAL18pd,
  /**
   * Mandibular left second molar mesial prosthesis
   */
  VAL18pm: v3_Dentition_VAL18pm,
  /**
   * Supernumerary mandibular left second molar
   */
  VAL18s: v3_Dentition_VAL18s,
  /**
   * Mandibular left first molar
   */
  VAL19: v3_Dentition_VAL19,
  /**
   * Mandibular left first molar abutment
   */
  VAL19a: v3_Dentition_VAL19a,
  /**
   * Mandibular left first molar abutment distal hemisection
   */
  VAL19ad: v3_Dentition_VAL19ad,
  /**
   * Mandibular left first molar abutment mesial hemisection
   */
  VAL19am: v3_Dentition_VAL19am,
  /**
   * Mandibular left first molar distal hemisection
   */
  VAL19d: v3_Dentition_VAL19d,
  /**
   * Mandibular left first molar implant
   */
  VAL19i: v3_Dentition_VAL19i,
  /**
   * Mandibular left first molar implant distal hemisection
   */
  VAL19id: v3_Dentition_VAL19id,
  /**
   * Mandibular left first molar implant mesial hemisection
   */
  VAL19im: v3_Dentition_VAL19im,
  /**
   * Mandibular left first molar mesial hemisection
   */
  VAL19m: v3_Dentition_VAL19m,
  /**
   * Mandibular left first molar prosthesis
   */
  VAL19p: v3_Dentition_VAL19p,
  /**
   * Mandibular left first molar distal prosthesis
   */
  VAL19pd: v3_Dentition_VAL19pd,
  /**
   * Mandibular left first molar mesial prosthesis
   */
  VAL19pm: v3_Dentition_VAL19pm,
  /**
   * Supernumerary mandibular left first molar
   */
  VAL19s: v3_Dentition_VAL19s,
  /**
   * Maxillary right third molar abutment
   */
  VAL1a: v3_Dentition_VAL1a,
  /**
   * Maxillary right third molar implant
   */
  VAL1i: v3_Dentition_VAL1i,
  /**
   * Maxillary right third molar prosthesis
   */
  VAL1p: v3_Dentition_VAL1p,
  /**
   * Maxillary right third molar distal prosthesis
   */
  VAL1pd: v3_Dentition_VAL1pd,
  /**
   * Maxillary right third molar mesial prosthesis
   */
  VAL1pm: v3_Dentition_VAL1pm,
  /**
   * Supernumerary maxillary right third molar
   */
  VAL1s: v3_Dentition_VAL1s,
  /**
   * Maxillary right second molar
   */
  VAL2: v3_Dentition_VAL2,
  /**
   * Mandibular left second premolar
   */
  VAL20: v3_Dentition_VAL20,
  /**
   * Mandibular left second premolar abutment
   */
  VAL20a: v3_Dentition_VAL20a,
  /**
   * Mandibular left second premolar implant
   */
  VAL20i: v3_Dentition_VAL20i,
  /**
   * Mandibular left second premolar prosthesis
   */
  VAL20p: v3_Dentition_VAL20p,
  /**
   * Mandibular left second premolar distal prosthesis
   */
  VAL20pd: v3_Dentition_VAL20pd,
  /**
   * Mandibular left second premolar mesial prosthesis
   */
  VAL20pm: v3_Dentition_VAL20pm,
  /**
   * Supernumerary mandibular left second premolar
   */
  VAL20s: v3_Dentition_VAL20s,
  /**
   * Mandibular left first premolar
   */
  VAL21: v3_Dentition_VAL21,
  /**
   * Mandibular left first premolar abutment
   */
  VAL21a: v3_Dentition_VAL21a,
  /**
   * Mandibular left first premolar implant
   */
  VAL21i: v3_Dentition_VAL21i,
  /**
   * Mandibular left first premolar prosthesis
   */
  VAL21p: v3_Dentition_VAL21p,
  /**
   * Mandibular left first premolar distal prosthesis
   */
  VAL21pd: v3_Dentition_VAL21pd,
  /**
   * Mandibular left first premolar mesial prosthesis
   */
  VAL21pm: v3_Dentition_VAL21pm,
  /**
   * Supernumerary mandibular left first premolar
   */
  VAL21s: v3_Dentition_VAL21s,
  /**
   * Mandibular left canine
   */
  VAL22: v3_Dentition_VAL22,
  /**
   * Mandibular left canine abutment
   */
  VAL22a: v3_Dentition_VAL22a,
  /**
   * Mandibular left canine implant
   */
  VAL22i: v3_Dentition_VAL22i,
  /**
   * Mandibular left canine prosthesis
   */
  VAL22p: v3_Dentition_VAL22p,
  /**
   * Mandibular left canine distal prosthesis
   */
  VAL22pd: v3_Dentition_VAL22pd,
  /**
   * Mandibular left canine mesial prosthesis
   */
  VAL22pm: v3_Dentition_VAL22pm,
  /**
   * Supernumerary mandibular left canine
   */
  VAL22s: v3_Dentition_VAL22s,
  /**
   * Mandibular left lateral incisor
   */
  VAL23: v3_Dentition_VAL23,
  /**
   * Mandibular left lateral incisor abutment
   */
  VAL23a: v3_Dentition_VAL23a,
  /**
   * Mandibular left lateral incisor implant
   */
  VAL23i: v3_Dentition_VAL23i,
  /**
   * Mandibular left lateral incisor prosthesis
   */
  VAL23p: v3_Dentition_VAL23p,
  /**
   * Mandibular left lateral incisor distal prosthesis
   */
  VAL23pd: v3_Dentition_VAL23pd,
  /**
   * Mandibular left lateral incisor mesial prosthesis
   */
  VAL23pm: v3_Dentition_VAL23pm,
  /**
   * Supernumerary mandibular left lateral incisor
   */
  VAL23s: v3_Dentition_VAL23s,
  /**
   * Mandibular left central incisor
   */
  VAL24: v3_Dentition_VAL24,
  /**
   * Mandibular left central incisor abutment
   */
  VAL24a: v3_Dentition_VAL24a,
  /**
   * Mandibular left central incisor implant
   */
  VAL24i: v3_Dentition_VAL24i,
  /**
   * Mandibular left central incisor prosthesis
   */
  VAL24p: v3_Dentition_VAL24p,
  /**
   * Mandibular left central incisor distal prosthesis
   */
  VAL24pd: v3_Dentition_VAL24pd,
  /**
   * Mandibular left central incisor mesial prosthesis
   */
  VAL24pm: v3_Dentition_VAL24pm,
  /**
   * Supernumerary mandibular left central incisor
   */
  VAL24s: v3_Dentition_VAL24s,
  /**
   * Mandibular right central incisor
   */
  VAL25: v3_Dentition_VAL25,
  /**
   * Mandibular right central incisor abutment
   */
  VAL25a: v3_Dentition_VAL25a,
  /**
   * Mandibular right central incisor implant
   */
  VAL25i: v3_Dentition_VAL25i,
  /**
   * Mandibular right central incisor prosthesis
   */
  VAL25p: v3_Dentition_VAL25p,
  /**
   * Mandibular right central incisor distal prosthesis
   */
  VAL25pd: v3_Dentition_VAL25pd,
  /**
   * Mandibular right central incisor mesial prosthesis
   */
  VAL25pm: v3_Dentition_VAL25pm,
  /**
   * Supernumerary mandibular right central incisor
   */
  VAL25s: v3_Dentition_VAL25s,
  /**
   * Mandibular right lateral incisor
   */
  VAL26: v3_Dentition_VAL26,
  /**
   * Mandibular right lateral incisor abutment
   */
  VAL26a: v3_Dentition_VAL26a,
  /**
   * Mandibular right lateral incisor implant
   */
  VAL26i: v3_Dentition_VAL26i,
  /**
   * Mandibular right lateral incisor prosthesis
   */
  VAL26p: v3_Dentition_VAL26p,
  /**
   * Mandibular right lateral incisor distal prosthesis
   */
  VAL26pd: v3_Dentition_VAL26pd,
  /**
   * Mandibular right lateral incisor mesial prosthesis
   */
  VAL26pm: v3_Dentition_VAL26pm,
  /**
   * Supernumerary mandibular right lateral incisor
   */
  VAL26s: v3_Dentition_VAL26s,
  /**
   * Mandibular right canine
   */
  VAL27: v3_Dentition_VAL27,
  /**
   * Mandibular right canine abutment
   */
  VAL27a: v3_Dentition_VAL27a,
  /**
   * Mandibular right canine implant
   */
  VAL27i: v3_Dentition_VAL27i,
  /**
   * Mandibular right canine prosthesis
   */
  VAL27p: v3_Dentition_VAL27p,
  /**
   * Mandibular right canine distal prosthesis
   */
  VAL27pd: v3_Dentition_VAL27pd,
  /**
   * Mandibular right canine mesial prosthesis
   */
  VAL27pm: v3_Dentition_VAL27pm,
  /**
   * Supernumerary mandibular right canine
   */
  VAL27s: v3_Dentition_VAL27s,
  /**
   * Mandibular right first premolar
   */
  VAL28: v3_Dentition_VAL28,
  /**
   * Mandibular right first premolar abutment
   */
  VAL28a: v3_Dentition_VAL28a,
  /**
   * Mandibular right first premolar implant
   */
  VAL28i: v3_Dentition_VAL28i,
  /**
   * Mandibular right first premolar prosthesis
   */
  VAL28p: v3_Dentition_VAL28p,
  /**
   * Mandibular right first premolar distal prosthesis
   */
  VAL28pd: v3_Dentition_VAL28pd,
  /**
   * Mandibular right first premolar mesial prosthesis
   */
  VAL28pm: v3_Dentition_VAL28pm,
  /**
   * Supernumerary mandibular right first premolar
   */
  VAL28s: v3_Dentition_VAL28s,
  /**
   * Mandibular right second premolar
   */
  VAL29: v3_Dentition_VAL29,
  /**
   * Mandibular right second premolar abutment
   */
  VAL29a: v3_Dentition_VAL29a,
  /**
   * Mandibular right second premolar implant
   */
  VAL29i: v3_Dentition_VAL29i,
  /**
   * Mandibular right second premolar prosthesis
   */
  VAL29p: v3_Dentition_VAL29p,
  /**
   * Mandibular right second premolar distal prosthesis
   */
  VAL29pd: v3_Dentition_VAL29pd,
  /**
   * Mandibular right second premolar mesial prosthesis
   */
  VAL29pm: v3_Dentition_VAL29pm,
  /**
   * Supernumerary mandibular right second premolar
   */
  VAL29s: v3_Dentition_VAL29s,
  /**
   * Maxillary right second molar abutment
   */
  VAL2a: v3_Dentition_VAL2a,
  /**
   * Maxillary right second molar implant
   */
  VAL2i: v3_Dentition_VAL2i,
  /**
   * Maxillary right second molar prosthesis
   */
  VAL2p: v3_Dentition_VAL2p,
  /**
   * Maxillary right second molar distal prosthesis
   */
  VAL2pd: v3_Dentition_VAL2pd,
  /**
   * Maxillary right second molar mesial prosthesis
   */
  VAL2pm: v3_Dentition_VAL2pm,
  /**
   * Supernumerary maxillary right second molar
   */
  VAL2s: v3_Dentition_VAL2s,
  /**
   * Maxillary right first molar
   */
  VAL3: v3_Dentition_VAL3,
  /**
   * Mandibular right first molar
   */
  VAL30: v3_Dentition_VAL30,
  /**
   * Mandibular right first molar abutment
   */
  VAL30a: v3_Dentition_VAL30a,
  /**
   * Mandibular right first molar abutment distal hemisection
   */
  VAL30ad: v3_Dentition_VAL30ad,
  /**
   * Mandibular right first molar abutment mesial hemisection
   */
  VAL30am: v3_Dentition_VAL30am,
  /**
   * Mandibular right first molar distal hemisection
   */
  VAL30d: v3_Dentition_VAL30d,
  /**
   * Mandibular right first molar implant
   */
  VAL30i: v3_Dentition_VAL30i,
  /**
   * Mandibular right first molar implant distal hemisection
   */
  VAL30id: v3_Dentition_VAL30id,
  /**
   * Mandibular right first molar implant mesial hemisection
   */
  VAL30im: v3_Dentition_VAL30im,
  /**
   * Mandibular right first molar mesial hemisection
   */
  VAL30m: v3_Dentition_VAL30m,
  /**
   * Mandibular right first molar prosthesis
   */
  VAL30p: v3_Dentition_VAL30p,
  /**
   * Mandibular right first molar distal prosthesis
   */
  VAL30pd: v3_Dentition_VAL30pd,
  /**
   * Mandibular right first molar mesial prosthesis
   */
  VAL30pm: v3_Dentition_VAL30pm,
  /**
   * Supernumerary mandibular right first molar
   */
  VAL30s: v3_Dentition_VAL30s,
  /**
   * Mandibular right second molar
   */
  VAL31: v3_Dentition_VAL31,
  /**
   * Mandibular right second molar abutment
   */
  VAL31a: v3_Dentition_VAL31a,
  /**
   * Mandibular right second molar abutment distal hemisection
   */
  VAL31ad: v3_Dentition_VAL31ad,
  /**
   * Mandibular right second molar abutment mesial hemisection
   */
  VAL31am: v3_Dentition_VAL31am,
  /**
   * Mandibular right second molar distal hemisection
   */
  VAL31d: v3_Dentition_VAL31d,
  /**
   * Mandibular right second molar implant
   */
  VAL31i: v3_Dentition_VAL31i,
  /**
   * Mandibular right second molar implant distal hemisection
   */
  VAL31id: v3_Dentition_VAL31id,
  /**
   * Mandibular right second molar implant mesial hemisection
   */
  VAL31im: v3_Dentition_VAL31im,
  /**
   * Mandibular right second molar mesial hemisection
   */
  VAL31m: v3_Dentition_VAL31m,
  /**
   * Mandibular right second molar prosthesis
   */
  VAL31p: v3_Dentition_VAL31p,
  /**
   * Mandibular right second molar distal prosthesis
   */
  VAL31pd: v3_Dentition_VAL31pd,
  /**
   * Mandibular right second molar mesial prosthesis
   */
  VAL31pm: v3_Dentition_VAL31pm,
  /**
   * Supernumerary mandibular right second molar
   */
  VAL31s: v3_Dentition_VAL31s,
  /**
   * Mandibular right third molar
   */
  VAL32: v3_Dentition_VAL32,
  /**
   * Mandibular right third molar abutment
   */
  VAL32a: v3_Dentition_VAL32a,
  /**
   * Mandibular right third molar abutment distal hemisection
   */
  VAL32ad: v3_Dentition_VAL32ad,
  /**
   * Mandibular right third molar abutment mesial hemisection
   */
  VAL32am: v3_Dentition_VAL32am,
  /**
   * Mandibular right third molar distal hemisection
   */
  VAL32d: v3_Dentition_VAL32d,
  /**
   * Mandibular right third molar implant
   */
  VAL32i: v3_Dentition_VAL32i,
  /**
   * Mandibular right third molar implant distal hemisection
   */
  VAL32id: v3_Dentition_VAL32id,
  /**
   * Mandibular right third molar implant mesial hemisection
   */
  VAL32im: v3_Dentition_VAL32im,
  /**
   * Mandibular right third molar mesial hemisection
   */
  VAL32m: v3_Dentition_VAL32m,
  /**
   * Mandibular right third molar prosthesis
   */
  VAL32p: v3_Dentition_VAL32p,
  /**
   * Mandibular right third molar distal prosthesis
   */
  VAL32pd: v3_Dentition_VAL32pd,
  /**
   * Mandibular right third molar mesial prosthesis
   */
  VAL32pm: v3_Dentition_VAL32pm,
  /**
   * Supernumerary mandibular right third molar
   */
  VAL32s: v3_Dentition_VAL32s,
  /**
   * Maxillary right first molar abutment
   */
  VAL3a: v3_Dentition_VAL3a,
  /**
   * Maxillary right first molar implant
   */
  VAL3i: v3_Dentition_VAL3i,
  /**
   * Maxillary right first molar prosthesis
   */
  VAL3p: v3_Dentition_VAL3p,
  /**
   * Maxillary right first molar distal prosthesis
   */
  VAL3pd: v3_Dentition_VAL3pd,
  /**
   * Maxillary right first molar mesial prosthesis
   */
  VAL3pm: v3_Dentition_VAL3pm,
  /**
   * Supernumerary maxillary right first molar
   */
  VAL3s: v3_Dentition_VAL3s,
  /**
   * Maxillary right second premolar
   */
  VAL4: v3_Dentition_VAL4,
  /**
   * Maxillary right second premolar abutment
   */
  VAL4a: v3_Dentition_VAL4a,
  /**
   * Maxillary right second premolar implant
   */
  VAL4i: v3_Dentition_VAL4i,
  /**
   * Maxillary right second premolar prosthesis
   */
  VAL4p: v3_Dentition_VAL4p,
  /**
   * Maxillary right second premolar distal prosthesis
   */
  VAL4pd: v3_Dentition_VAL4pd,
  /**
   * Maxillary right second premolar mesial prosthesis
   */
  VAL4pm: v3_Dentition_VAL4pm,
  /**
   * Supernumerary maxillary right second premolar
   */
  VAL4s: v3_Dentition_VAL4s,
  /**
   * Maxillary right first premolar
   */
  VAL5: v3_Dentition_VAL5,
  /**
   * Maxillary right first premolar abutment
   */
  VAL5a: v3_Dentition_VAL5a,
  /**
   * Maxillary right first premolar implant
   */
  VAL5i: v3_Dentition_VAL5i,
  /**
   * Maxillary right first premolar prosthesis
   */
  VAL5p: v3_Dentition_VAL5p,
  /**
   * Maxillary right first premolar distal prosthesis
   */
  VAL5pd: v3_Dentition_VAL5pd,
  /**
   * Maxillary right first premolar mesial prosthesis
   */
  VAL5pm: v3_Dentition_VAL5pm,
  /**
   * Supernumerary maxillary right first premolar
   */
  VAL5s: v3_Dentition_VAL5s,
  /**
   * Maxillary right canine
   */
  VAL6: v3_Dentition_VAL6,
  /**
   * Maxillary right canine abutment
   */
  VAL6a: v3_Dentition_VAL6a,
  /**
   * Maxillary right canine implant
   */
  VAL6i: v3_Dentition_VAL6i,
  /**
   * Maxillary right canine prosthesis
   */
  VAL6p: v3_Dentition_VAL6p,
  /**
   * Maxillary right canine distal prosthesis
   */
  VAL6pd: v3_Dentition_VAL6pd,
  /**
   * Maxillary right canine mesial prosthesis
   */
  VAL6pm: v3_Dentition_VAL6pm,
  /**
   * Supernumerary maxillary right canine
   */
  VAL6s: v3_Dentition_VAL6s,
  /**
   * Maxillary right lateral incisor
   */
  VAL7: v3_Dentition_VAL7,
  /**
   * Maxillary right lateral incisor abutment
   */
  VAL7a: v3_Dentition_VAL7a,
  /**
   * Maxillary right lateral incisor implant
   */
  VAL7i: v3_Dentition_VAL7i,
  /**
   * Maxillary right lateral incisor prosthesis
   */
  VAL7p: v3_Dentition_VAL7p,
  /**
   * Maxillary right lateral incisor distal prosthesis
   */
  VAL7pd: v3_Dentition_VAL7pd,
  /**
   * Maxillary right lateral incisor mesial prosthesis
   */
  VAL7pm: v3_Dentition_VAL7pm,
  /**
   * Supernumerary maxillary right lateral incisor
   */
  VAL7s: v3_Dentition_VAL7s,
  /**
   * Maxillary right central incisor
   */
  VAL8: v3_Dentition_VAL8,
  /**
   * Maxillary right central incisor abutment
   */
  VAL8a: v3_Dentition_VAL8a,
  /**
   * Maxillary right central incisor implant
   */
  VAL8i: v3_Dentition_VAL8i,
  /**
   * Maxillary right central incisor prosthesis
   */
  VAL8p: v3_Dentition_VAL8p,
  /**
   * Maxillary right central incisor distal prosthesis
   */
  VAL8pd: v3_Dentition_VAL8pd,
  /**
   * Maxillary right central incisor mesial prosthesis
   */
  VAL8pm: v3_Dentition_VAL8pm,
  /**
   * Supernumerary maxillary right central incisor
   */
  VAL8s: v3_Dentition_VAL8s,
  /**
   * Maxillary left central incisor
   */
  VAL9: v3_Dentition_VAL9,
  /**
   * Maxillary left central incisor abutment
   */
  VAL9a: v3_Dentition_VAL9a,
  /**
   * Maxillary left central incisor implant
   */
  VAL9i: v3_Dentition_VAL9i,
  /**
   * Maxillary left central incisor prosthesis
   */
  VAL9p: v3_Dentition_VAL9p,
  /**
   * Maxillary left central incisor distal prosthesis
   */
  VAL9pd: v3_Dentition_VAL9pd,
  /**
   * Maxillary left central incisor mesial prosthesis
   */
  VAL9pm: v3_Dentition_VAL9pm,
  /**
   * Supernumerary maxillary left central incisor
   */
  VAL9s: v3_Dentition_VAL9s,
  /**
   * Maxillary right second primary molar
   */
  A: v3_Dentition_A,
  /**
   * Supernumerary maxillary right second primary molar
   */
  As: v3_Dentition_As,
  /**
   * Maxillary right first primary molar
   */
  B: v3_Dentition_B,
  /**
   * Supernumerary maxillary right first primary molar
   */
  Bs: v3_Dentition_Bs,
  /**
   * Maxillary right primary canine
   */
  C: v3_Dentition_C,
  /**
   * Supernumerary maxillary right primary canine
   */
  Cs: v3_Dentition_Cs,
  /**
   * Maxillary right lateral primary incisor
   */
  D: v3_Dentition_D,
  /**
   * Supernumerary maxillary right lateral primary incisor
   */
  Ds: v3_Dentition_Ds,
  /**
   * Maxillary right central primary incisor
   */
  E: v3_Dentition_E,
  /**
   * Supernumerary maxillary right central primary incisor
   */
  Es: v3_Dentition_Es,
  /**
   * Maxillary left central primary incisor
   */
  F: v3_Dentition_F,
  /**
   * Supernumerary maxillary left central primary incisor
   */
  Fs: v3_Dentition_Fs,
  /**
   * Maxillary left lateral primary incisor
   */
  G: v3_Dentition_G,
  /**
   * Supernumerary maxillary left lateral primary incisor
   */
  Gs: v3_Dentition_Gs,
  /**
   * Maxillary left primary canine
   */
  H: v3_Dentition_H,
  /**
   * Supernumerary maxillary left primary canine
   */
  Hs: v3_Dentition_Hs,
  /**
   * Maxillary left first primary molar
   */
  I: v3_Dentition_I,
  /**
   * Supernumerary maxillary left first primary molar
   */
  Is: v3_Dentition_Is,
  /**
   * Maxillary left second primary molar
   */
  J: v3_Dentition_J,
  /**
   * Supernumerary maxillary left second primary molar
   */
  Js: v3_Dentition_Js,
  /**
   * Mandibular left second primary molar
   */
  K: v3_Dentition_K,
  /**
   * Supernumerary mandibular left second primary molar
   */
  Ks: v3_Dentition_Ks,
  /**
   * Mandibular left first primary molar
   */
  L: v3_Dentition_L,
  /**
   * Supernumerary mandibular left first primary molar
   */
  Ls: v3_Dentition_Ls,
  /**
   * Mandibular left primary canine
   */
  M: v3_Dentition_M,
  /**
   * Supernumerary mandibular left primary canine
   */
  Ms: v3_Dentition_Ms,
  /**
   * Mandibular left lateral primary incisor
   */
  N: v3_Dentition_N,
  /**
   * Supernumerary mandibular left lateral primary incisor
   */
  Ns: v3_Dentition_Ns,
  /**
   * Mandibular left central primary incisor
   */
  O: v3_Dentition_O,
  /**
   * Supernumerary mandibular left central primary incisor
   */
  Os: v3_Dentition_Os,
  /**
   * Mandibular right central primary incisor
   */
  P: v3_Dentition_P,
  /**
   * Supernumerary mandibular right central primary incisor
   */
  Ps: v3_Dentition_Ps,
  /**
   * Mandibular right lateral primary incisor
   */
  Q: v3_Dentition_Q,
  /**
   * Supernumerary mandibular right lateral primary incisor
   */
  Qs: v3_Dentition_Qs,
  /**
   * Mandibular right primary canine
   */
  R: v3_Dentition_R,
  /**
   * Supernumerary mandibular right primary canine
   */
  Rs: v3_Dentition_Rs,
  /**
   * Mandibular right first primary molar
   */
  S: v3_Dentition_S,
  /**
   * Supernumerary mandibular right first primary molar
   */
  Ss: v3_Dentition_Ss,
  /**
   * Mandibular right second primary molar
   */
  T: v3_Dentition_T,
  /**
   * Supernumerary mandibular right second primary molar
   */
  Ts: v3_Dentition_Ts,
};
const v3_DeviceAlertLevel_Critical: Coding = {
  code: 'C',
  display: 'Critical',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DeviceAlertLevel',
};
const v3_DeviceAlertLevel_Normal: Coding = {
  code: 'N',
  display: 'Normal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DeviceAlertLevel',
};
const v3_DeviceAlertLevel_Serious: Coding = {
  code: 'S',
  display: 'Serious',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DeviceAlertLevel',
};
const v3_DeviceAlertLevel_Warning: Coding = {
  code: 'W',
  display: 'Warning',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DeviceAlertLevel',
};
/**
 *  Domain values for the Device.Alert_levelCode
 */
export const V3DeviceAlertLevel = {
  /**
   * Shut Down, Fix Problem and Re-initialize
   */
  Critical: v3_DeviceAlertLevel_Critical,
  /**
   * No Corrective Action Needed
   */
  Normal: v3_DeviceAlertLevel_Normal,
  /**
   * Corrective Action Required
   */
  Serious: v3_DeviceAlertLevel_Serious,
  /**
   * Corrective Action Anticipated
   */
  Warning: v3_DeviceAlertLevel_Warning,
};
const v3_DocumentCompletion_Authenticated: Coding = {
  code: 'AU',
  display: 'authenticated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DocumentCompletion',
};
const v3_DocumentCompletion_Dictated: Coding = {
  code: 'DI',
  display: 'dictated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DocumentCompletion',
};
const v3_DocumentCompletion_Documented: Coding = {
  code: 'DO',
  display: 'documented',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DocumentCompletion',
};
const v3_DocumentCompletion_Incomplete: Coding = {
  code: 'IN',
  display: 'incomplete',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DocumentCompletion',
};
const v3_DocumentCompletion_InProgress: Coding = {
  code: 'IP',
  display: 'in progress',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DocumentCompletion',
};
const v3_DocumentCompletion_NullifiedDocument: Coding = {
  code: 'NU',
  display: 'nullified document',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DocumentCompletion',
};
const v3_DocumentCompletion_PreAuthenticated: Coding = {
  code: 'PA',
  display: 'pre-authenticated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DocumentCompletion',
};
const v3_DocumentCompletion_UnsignedCompletedDocument: Coding = {
  code: 'UC',
  display: 'unsigned completed document',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DocumentCompletion',
};
/**
 *  Identifies the current completion state of a clinical document.
 */
export const V3DocumentCompletion = {
  /**
   * A completion status in which a document has been signed manually or electronically by one or more individuals who attest to its accuracy.  No explicit determination is made that the assigned individual has performed the authentication.  While the standard allows multiple instances of authentication, it would be typical to have a single instance of authentication, usually by the assigned individual.
   */
  Authenticated: v3_DocumentCompletion_Authenticated,
  /**
   * A completion status in which information has been orally recorded but not yet transcribed.
   */
  Dictated: v3_DocumentCompletion_Dictated,
  /**
   * A completion status in which document content, other than dictation, has been received but has not been translated into the final electronic format.  Examples include paper documents, whether hand-written or typewritten, and intermediate electronic forms, such as voice to text.
   */
  Documented: v3_DocumentCompletion_Documented,
  /**
   * A completion status in which information is known to be missing from a transcribed document.
   */
  Incomplete: v3_DocumentCompletion_Incomplete,
  /**
   * A workflow status where the material has been assigned to personnel to perform the task of transcription. The document remains in this state until the document is transcribed.
   */
  InProgress: v3_DocumentCompletion_InProgress,
  /**
   * A completion status in which a document has been signed manually or electronically by the individual who is legally responsible for that document. This is the most mature state in the workflow progression.
   */
  LegallyAuthenticated: v3_DocumentCompletion_LegallyAuthenticated,
  /**
   * A completion status in which a document was created in error or was placed in the wrong chart. The document is no longer available.
   */
  NullifiedDocument: v3_DocumentCompletion_NullifiedDocument,
  /**
   * A completion status in which a document is transcribed but not authenticated.
   */
  PreAuthenticated: v3_DocumentCompletion_PreAuthenticated,
  /**
   * A completion status where the document is complete and there is no expectation that the document will be signed.
   */
  UnsignedCompletedDocument: v3_DocumentCompletion_UnsignedCompletedDocument,
};
const v3_DocumentStorage_ActiveAndArchived: Coding = {
  code: 'AA',
  display: 'active and archived',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DocumentStorage',
};
const v3_DocumentStorage_Active: Coding = {
  code: 'AC',
  display: 'active',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DocumentStorage',
};
const v3_DocumentStorage_ArchivedNotActive: Coding = {
  code: 'AR',
  display: 'archived (not active)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DocumentStorage',
};
const v3_DocumentStorage_Purged: Coding = {
  code: 'PU',
  display: 'purged',
  system: 'http://terminology.hl7.org/CodeSystem/v3-DocumentStorage',
};
/**
 *  Identifies the storage status of a document.
 */
export const V3DocumentStorage = {
  /**
   * A storage status in which a document is available on-line and is also stored off-line for long-term access.
   */
  ActiveAndArchived: v3_DocumentStorage_ActiveAndArchived,
  /**
   * A storage status in which a document is available on-line.
   */
  Active: v3_DocumentStorage_Active,
  /**
   * A storage status in which a document has been stored off-line for long-term access.
   */
  ArchivedNotActive: v3_DocumentStorage_ArchivedNotActive,
  /**
   * A storage status in which a document is no longer available in this system.
   */
  Purged: v3_DocumentStorage_Purged,
};
const v3_EducationLevel_AssociateQuoteSOrTechnicalDegreeComplete: Coding = {
  code: 'ASSOC',
  display: "Associate's or technical degree complete",
  system: 'http://terminology.hl7.org/CodeSystem/v3-EducationLevel',
};
const v3_EducationLevel_CollegeOrBaccalaureateDegreeComplete: Coding = {
  code: 'BD',
  display: 'College or baccalaureate degree complete',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EducationLevel',
};
const v3_EducationLevel_ElementarySchool: Coding = {
  code: 'ELEM',
  display: 'Elementary School',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EducationLevel',
};
const v3_EducationLevel_GraduateOrProfessionalDegreeComplete: Coding = {
  code: 'GD',
  display: 'Graduate or professional Degree complete',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EducationLevel',
};
const v3_EducationLevel_HighSchoolOrSecondarySchoolDegreeComplete: Coding = {
  code: 'HS',
  display: 'High School or secondary school degree complete',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EducationLevel',
};
const v3_EducationLevel_SomePostBaccalaureateEducation: Coding = {
  code: 'PB',
  display: 'Some post-baccalaureate education',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EducationLevel',
};
const v3_EducationLevel_DoctoralOrPostGraduateEducation: Coding = {
  code: 'POSTG',
  display: 'Doctoral or post graduate education',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EducationLevel',
};
const v3_EducationLevel_SomeCollegeEducation: Coding = {
  code: 'SCOL',
  display: 'Some College education',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EducationLevel',
};
const v3_EducationLevel_SomeSecondaryOrHighSchoolEducation: Coding = {
  code: 'SEC',
  display: 'Some secondary or high school education',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EducationLevel',
};
/**
 *  Years of education that a person has completed
 */
export const V3EducationLevel = {
  /**
   * Associate's or technical degree complete
   */
  AssociateQuoteSOrTechnicalDegreeComplete: v3_EducationLevel_AssociateQuoteSOrTechnicalDegreeComplete,
  /**
   * College or baccalaureate degree complete
   */
  CollegeOrBaccalaureateDegreeComplete: v3_EducationLevel_CollegeOrBaccalaureateDegreeComplete,
  /**
   * Elementary School
   */
  ElementarySchool: v3_EducationLevel_ElementarySchool,
  /**
   * Graduate or professional Degree complete
   */
  GraduateOrProfessionalDegreeComplete: v3_EducationLevel_GraduateOrProfessionalDegreeComplete,
  /**
   * High School or secondary school degree complete
   */
  HighSchoolOrSecondarySchoolDegreeComplete: v3_EducationLevel_HighSchoolOrSecondarySchoolDegreeComplete,
  /**
   * Some post-baccalaureate education
   */
  SomePostBaccalaureateEducation: v3_EducationLevel_SomePostBaccalaureateEducation,
  /**
   * Doctoral or post graduate education
   */
  DoctoralOrPostGraduateEducation: v3_EducationLevel_DoctoralOrPostGraduateEducation,
  /**
   * Some College education
   */
  SomeCollegeEducation: v3_EducationLevel_SomeCollegeEducation,
  /**
   * Some secondary or high school education
   */
  SomeSecondaryOrHighSchoolEducation: v3_EducationLevel_SomeSecondaryOrHighSchoolEducation,
};
const v3_EmployeeJobClass_FullTime: Coding = {
  code: 'FT',
  display: 'full-time',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EmployeeJobClass',
};
const v3_EmployeeJobClass_PartTime: Coding = {
  code: 'PT',
  display: 'part-time',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EmployeeJobClass',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3EmployeeJobClass = {
  /**
   * Employment in which the employee is expected to work at least a standard work week (defined by the US Bureau of Labor Statistics as 35-44 hours per week)
   */
  FullTime: v3_EmployeeJobClass_FullTime,
  /**
   * Employment in which the employee is expected to work less than a standard work week (defined by the US Bureau of Labor Statistics as 35-44 hours per week)
   */
  PartTime: v3_EmployeeJobClass_PartTime,
};
const v3_ObservationValue_Employed: Coding = {
  code: 'Employed',
  display: 'Employed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_NotInLaborForce: Coding = {
  code: 'NotInLaborForce',
  display: 'Not In Labor Force',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Unemployed: Coding = {
  code: 'Unemployed',
  display: 'Unemployed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
/**
 *  Concepts representing whether a person does or does not currently have a job or is not currently in the labor pool seeking employment.
 */
export const V3EmploymentStatusODH = {
  /**
   * Individuals who, during the last week: a) did any work for at least 1 hour as paid or unpaid employees of a business or government organization; worked in their own businesses, professions, or on their own farms; or b) were not working, but who have a job or business from which the individual was temporarily absent because of vacation, illness, bad weather, childcare problems, maternity or paternity leave, labor-management dispute, job training, or other family or personal reasons, regardless of whether or not they were paid for the time off or were seeking other jobs.
   */
  Employed: v3_ObservationValue_Employed,
  /**
   * Persons not classified as employed or unemployed, meaning those who have no job and are not looking for one.
   */
  NotInLaborForce: v3_ObservationValue_NotInLaborForce,
  /**
   * Persons who currently have no employment, but are available for work and have made specific efforts to find employment.
   */
  Unemployed: v3_ObservationValue_Unemployed,
};
const v3_EncounterAdmissionSource_Emergency: Coding = {
  code: 'E',
  display: 'emergency',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EncounterAdmissionSource',
};
const v3_EncounterAdmissionSource_LaborAndDelivery: Coding = {
  code: 'LD',
  display: 'labor and delivery',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EncounterAdmissionSource',
};
const v3_EncounterAdmissionSource_Newborn: Coding = {
  code: 'NB',
  display: 'newborn',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EncounterAdmissionSource',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3EncounterAdmissionSource = {
  /**
   * emergency
   */
  Emergency: v3_EncounterAdmissionSource_Emergency,
  /**
   * labor and delivery
   */
  LaborAndDelivery: v3_EncounterAdmissionSource_LaborAndDelivery,
  /**
   * newborn
   */
  Newborn: v3_EncounterAdmissionSource_Newborn,
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3EncounterSpecialCourtesy = {
  /**
   * extended courtesy
   */
  ExtendedCourtesy: v3_EncounterSpecialCourtesy_ExtendedCourtesy,
  /**
   * normal courtesy
   */
  NormalCourtesy: v3_EncounterSpecialCourtesy_NormalCourtesy,
  /**
   * professional courtesy
   */
  ProfessionalCourtesy: v3_EncounterSpecialCourtesy_ProfessionalCourtesy,
  /**
   * Courtesies extended to the staff of the entity providing service.
   */
  Staff: v3_EncounterSpecialCourtesy_Staff,
  /**
   * very important person
   */
  VeryImportantPerson: v3_EncounterSpecialCourtesy_VeryImportantPerson,
};
const v3_EntityClass_Animal: Coding = {
  code: 'ANM',
  display: 'animal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_CertificateRepresentation: Coding = {
  code: 'CER',
  display: 'certificate representation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_ChemicalSubstance: Coding = {
  code: 'CHEM',
  display: 'chemical substance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_CityOrTown: Coding = {
  code: 'CITY',
  display: 'city or town',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Container: Coding = {
  code: 'CONT',
  display: 'container',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Country: Coding = {
  code: 'COUNTRY',
  display: 'country',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_CountyOrParish: Coding = {
  code: 'COUNTY',
  display: 'county or parish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Device: Coding = {
  code: 'DEV',
  display: 'device',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Entity: Coding = {
  code: 'ENT',
  display: 'entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Food: Coding = {
  code: 'FOOD',
  display: 'food',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_HealthChartEntity: Coding = {
  code: 'HCE',
  display: 'health chart entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Holder: Coding = {
  code: 'HOLD',
  display: 'holder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_LivingSubject: Coding = {
  code: 'LIV',
  display: 'living subject',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Material: Coding = {
  code: 'MAT',
  display: 'material',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Microorganism: Coding = {
  code: 'MIC',
  display: 'microorganism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_ManufacturedMaterial: Coding = {
  code: 'MMAT',
  display: 'manufactured material',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_ImagingModality: Coding = {
  code: 'MODDV',
  display: 'imaging modality',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Nation: Coding = {
  code: 'NAT',
  display: 'Nation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_NonPersonLivingSubject: Coding = {
  code: 'NLIV',
  display: 'non-person living subject',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Organization: Coding = {
  code: 'ORG',
  display: 'organization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Place: Coding = {
  code: 'PLC',
  display: 'place',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Plant: Coding = {
  code: 'PLNT',
  display: 'plant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_StateOrProvince: Coding = {
  code: 'PROVINCE',
  display: 'state or province',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Person: Coding = {
  code: 'PSN',
  display: 'person',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_PublicInstitution: Coding = {
  code: 'PUB',
  display: 'public institution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_Group: Coding = {
  code: 'RGRP',
  display: 'group',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
const v3_EntityClass_State: Coding = {
  code: 'STATE',
  display: 'state',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityClass',
};
/**
 *  Classifies the Entity class and all of its subclasses.  The terminology is hierarchical.  At the top is this  HL7-defined domain of high-level categories (such as represented by the Entity subclasses). Each of these terms must be harmonized and is specializable. The value sets beneath are drawn from multiple, frequently external, domains that reflect much more fine-grained typing.
 */
export const V3EntityClass = {
  /**
   * A living subject from the animal kingdom.
   */
  Animal: v3_EntityClass_Animal,
  /**
   * A physical artifact that stores information about the granting of authorization.
   */
  CertificateRepresentation: v3_EntityClass_CertificateRepresentation,
  /**
   * A substance that is fully defined by an organic or inorganic chemical formula, includes mixtures of other chemical substances. Refine using, e.g., IUPAC codes.
   */
  ChemicalSubstance: v3_EntityClass_ChemicalSubstance,
  /**
   * The territory of a city, town or other municipality.
   */
  CityOrTown: v3_EntityClass_CityOrTown,
  /**
   * A container of other entities.
   */
  Container: v3_EntityClass_Container,
  /**
   * The territory of a sovereign nation.
   */
  Country: v3_EntityClass_Country,
  /**
   * The territory of a county, parish or other division of a state or province.
   */
  CountyOrParish: v3_EntityClass_CountyOrParish,
  /**
   * A subtype of ManufacturedMaterial used in an activity, without being substantially changed through that activity.  The kind of device is identified by the code attribute inherited from Entity.
   *
   *
   *                            Usage: This includes durable (reusable) medical equipment as well as disposable equipment.
   */
  Device: v3_EntityClass_Device,
  /**
   * Corresponds to the Entity class
   */
  Entity: v3_EntityClass_Entity,
  /**
   * Naturally occurring, processed or manufactured entities that are primarily used as food for humans and animals.
   */
  Food: v3_EntityClass_Food,
  /**
   * A health chart included to serve as a document receiving entity in the management of medical records.
   */
  HealthChartEntity: v3_EntityClass_HealthChartEntity,
  /**
   * A type of container that can hold other containers or other holders.
   */
  Holder: v3_EntityClass_Holder,
  /**
   * Anything that essentially has the property of life, independent of current state (a dead human corpse is still essentially a living subject).
   */
  LivingSubject: v3_EntityClass_LivingSubject,
  /**
   * Any thing that has extension in space and mass, may be of living or non-living origin.
   */
  Material: v3_EntityClass_Material,
  /**
   * All single celled living organisms including protozoa, bacteria, yeast, viruses, etc.
   */
  Microorganism: v3_EntityClass_Microorganism,
  /**
   * Corresponds to the ManufacturedMaterial class
   */
  ManufacturedMaterial: v3_EntityClass_ManufacturedMaterial,
  /**
   * Class to contain unique attributes of diagnostic imaging equipment.
   */
  ImagingModality: v3_EntityClass_ImagingModality,
  /**
   * A politically organized body of people bonded by territory and known as a nation.
   */
  Nation: v3_EntityClass_Nation,
  /**
   * A subtype of living subject that includes all living things except the species Homo Sapiens.
   */
  NonPersonLivingSubject: v3_EntityClass_NonPersonLivingSubject,
  /**
   * A social or legal structure formed by human beings.
   */
  Organization: v3_EntityClass_Organization,
  /**
   * A physical place or site with its containing structure. May be natural or man-made. The geographic position of a place may or may not be constant.
   */
  Place: v3_EntityClass_Place,
  /**
   * A living subject from the order of plants.
   */
  Plant: v3_EntityClass_Plant,
  /**
   * The territory of a state, province, department or other division of a sovereign country.
   */
  StateOrProvince: v3_EntityClass_StateOrProvince,
  /**
   * A living subject of the species homo sapiens.
   */
  Person: v3_EntityClass_Person,
  /**
   * An agency of the people of a state often assuming some authority over a certain matter. Includes government, governmental agencies, associations.
   */
  PublicInstitution: v3_EntityClass_PublicInstitution,
  /**
   * A grouping of resources (personnel, material, or places) to be used for scheduling purposes.  May be a pool of like-type resources, a team, or combination of personnel, material and places.
   */
  Group: v3_EntityClass_Group,
  /**
   * A politically organized body of people bonded by territory, culture, or ethnicity, having sovereignty (to a certain extent) granted by other states (enclosing or neighboring states). This includes countries (nations), provinces (e.g., one of the United States of America or a French departement), counties or municipalities. Refine using, e.g., ISO country codes, FIPS-PUB state codes, etc.
   */
  State: v3_EntityClass_State,
};
/**
 *  A subtype of ManufacturedMaterial used in an activity, without being substantially changed through that activity.  The kind of device is identified by the code attribute inherited from Entity.  Usage:
 * This includes durable (reusable) medical equipment as well as disposable equipment.
 */
export const V3EntityClassDevice = {
  /**
   * A physical artifact that stores information about the granting of authorization.
   */
  CertificateRepresentation: v3_EntityClass_CertificateRepresentation,
  /**
   * A subtype of ManufacturedMaterial used in an activity, without being substantially changed through that activity.  The kind of device is identified by the code attribute inherited from Entity.
   *
   *
   *                            Usage: This includes durable (reusable) medical equipment as well as disposable equipment.
   */
  Device: v3_EntityClass_Device,
  /**
   * Class to contain unique attributes of diagnostic imaging equipment.
   */
  ImagingModality: v3_EntityClass_ImagingModality,
};
/**
 *  Anything that essentially has the property of life, independent of current state (a dead human corpse is still essentially a living subject.)
 */
export const V3EntityClassLivingSubject = {
  /**
   * A living subject from the animal kingdom.
   */
  Animal: v3_EntityClass_Animal,
  /**
   * Anything that essentially has the property of life, independent of current state (a dead human corpse is still essentially a living subject).
   */
  LivingSubject: v3_EntityClass_LivingSubject,
  /**
   * All single celled living organisms including protozoa, bacteria, yeast, viruses, etc.
   */
  Microorganism: v3_EntityClass_Microorganism,
  /**
   * A subtype of living subject that includes all living things except the species Homo Sapiens.
   */
  NonPersonLivingSubject: v3_EntityClass_NonPersonLivingSubject,
  /**
   * A living subject from the order of plants.
   */
  Plant: v3_EntityClass_Plant,
  /**
   * A living subject of the species homo sapiens.
   */
  Person: v3_EntityClass_Person,
};
/**
 *  Corresponds to the ManufacturedMaterial class
 */
export const V3EntityClassManufacturedMaterial = {
  /**
   * A physical artifact that stores information about the granting of authorization.
   */
  CertificateRepresentation: v3_EntityClass_CertificateRepresentation,
  /**
   * A container of other entities.
   */
  Container: v3_EntityClass_Container,
  /**
   * A subtype of ManufacturedMaterial used in an activity, without being substantially changed through that activity.  The kind of device is identified by the code attribute inherited from Entity.
   *
   *
   *                            Usage: This includes durable (reusable) medical equipment as well as disposable equipment.
   */
  Device: v3_EntityClass_Device,
  /**
   * A type of container that can hold other containers or other holders.
   */
  Holder: v3_EntityClass_Holder,
  /**
   * Corresponds to the ManufacturedMaterial class
   */
  ManufacturedMaterial: v3_EntityClass_ManufacturedMaterial,
  /**
   * Class to contain unique attributes of diagnostic imaging equipment.
   */
  ImagingModality: v3_EntityClass_ImagingModality,
};
/**
 *  A social or legal structure formed by human beings.
 */
export const V3EntityClassOrganization = {
  /**
   * A politically organized body of people bonded by territory and known as a nation.
   */
  Nation: v3_EntityClass_Nation,
  /**
   * A social or legal structure formed by human beings.
   */
  Organization: v3_EntityClass_Organization,
  /**
   * An agency of the people of a state often assuming some authority over a certain matter. Includes government, governmental agencies, associations.
   */
  PublicInstitution: v3_EntityClass_PublicInstitution,
  /**
   * A politically organized body of people bonded by territory, culture, or ethnicity, having sovereignty (to a certain extent) granted by other states (enclosing or neighboring states). This includes countries (nations), provinces (e.g., one of the United States of America or a French departement), counties or municipalities. Refine using, e.g., ISO country codes, FIPS-PUB state codes, etc.
   */
  State: v3_EntityClass_State,
};
/**
 *  A physicial place or site with its containing structure. May be natural or man-made.  The geographic position of a place may or may not be constant.
 */
export const V3EntityClassPlace = {
  /**
   * The territory of a city, town or other municipality.
   */
  CityOrTown: v3_EntityClass_CityOrTown,
  /**
   * The territory of a sovereign nation.
   */
  Country: v3_EntityClass_Country,
  /**
   * The territory of a county, parish or other division of a state or province.
   */
  CountyOrParish: v3_EntityClass_CountyOrParish,
  /**
   * A physical place or site with its containing structure. May be natural or man-made. The geographic position of a place may or may not be constant.
   */
  Place: v3_EntityClass_Place,
  /**
   * The territory of a state, province, department or other division of a sovereign country.
   */
  StateOrProvince: v3_EntityClass_StateOrProvince,
};
/**
 *  Corresponds to the Entity class
 */
export const V3EntityClassRoot = {
  /**
   * A living subject from the animal kingdom.
   */
  Animal: v3_EntityClass_Animal,
  /**
   * A physical artifact that stores information about the granting of authorization.
   */
  CertificateRepresentation: v3_EntityClass_CertificateRepresentation,
  /**
   * A substance that is fully defined by an organic or inorganic chemical formula, includes mixtures of other chemical substances. Refine using, e.g., IUPAC codes.
   */
  ChemicalSubstance: v3_EntityClass_ChemicalSubstance,
  /**
   * The territory of a city, town or other municipality.
   */
  CityOrTown: v3_EntityClass_CityOrTown,
  /**
   * A container of other entities.
   */
  Container: v3_EntityClass_Container,
  /**
   * The territory of a sovereign nation.
   */
  Country: v3_EntityClass_Country,
  /**
   * The territory of a county, parish or other division of a state or province.
   */
  CountyOrParish: v3_EntityClass_CountyOrParish,
  /**
   * A subtype of ManufacturedMaterial used in an activity, without being substantially changed through that activity.  The kind of device is identified by the code attribute inherited from Entity.
   *
   *
   *                            Usage: This includes durable (reusable) medical equipment as well as disposable equipment.
   */
  Device: v3_EntityClass_Device,
  /**
   * Corresponds to the Entity class
   */
  Entity: v3_EntityClass_Entity,
  /**
   * Naturally occurring, processed or manufactured entities that are primarily used as food for humans and animals.
   */
  Food: v3_EntityClass_Food,
  /**
   * A health chart included to serve as a document receiving entity in the management of medical records.
   */
  HealthChartEntity: v3_EntityClass_HealthChartEntity,
  /**
   * A type of container that can hold other containers or other holders.
   */
  Holder: v3_EntityClass_Holder,
  /**
   * Anything that essentially has the property of life, independent of current state (a dead human corpse is still essentially a living subject).
   */
  LivingSubject: v3_EntityClass_LivingSubject,
  /**
   * Any thing that has extension in space and mass, may be of living or non-living origin.
   */
  Material: v3_EntityClass_Material,
  /**
   * All single celled living organisms including protozoa, bacteria, yeast, viruses, etc.
   */
  Microorganism: v3_EntityClass_Microorganism,
  /**
   * Corresponds to the ManufacturedMaterial class
   */
  ManufacturedMaterial: v3_EntityClass_ManufacturedMaterial,
  /**
   * Class to contain unique attributes of diagnostic imaging equipment.
   */
  ImagingModality: v3_EntityClass_ImagingModality,
  /**
   * A politically organized body of people bonded by territory and known as a nation.
   */
  Nation: v3_EntityClass_Nation,
  /**
   * A subtype of living subject that includes all living things except the species Homo Sapiens.
   */
  NonPersonLivingSubject: v3_EntityClass_NonPersonLivingSubject,
  /**
   * A social or legal structure formed by human beings.
   */
  Organization: v3_EntityClass_Organization,
  /**
   * A physical place or site with its containing structure. May be natural or man-made. The geographic position of a place may or may not be constant.
   */
  Place: v3_EntityClass_Place,
  /**
   * A living subject from the order of plants.
   */
  Plant: v3_EntityClass_Plant,
  /**
   * The territory of a state, province, department or other division of a sovereign country.
   */
  StateOrProvince: v3_EntityClass_StateOrProvince,
  /**
   * A living subject of the species homo sapiens.
   */
  Person: v3_EntityClass_Person,
  /**
   * An agency of the people of a state often assuming some authority over a certain matter. Includes government, governmental agencies, associations.
   */
  PublicInstitution: v3_EntityClass_PublicInstitution,
  /**
   * A grouping of resources (personnel, material, or places) to be used for scheduling purposes.  May be a pool of like-type resources, a team, or combination of personnel, material and places.
   */
  Group: v3_EntityClass_Group,
  /**
   * A politically organized body of people bonded by territory, culture, or ethnicity, having sovereignty (to a certain extent) granted by other states (enclosing or neighboring states). This includes countries (nations), provinces (e.g., one of the United States of America or a French departement), counties or municipalities. Refine using, e.g., ISO country codes, FIPS-PUB state codes, etc.
   */
  State: v3_EntityClass_State,
};
const v3_EntityCode_AccessMedicalDevice: Coding = {
  code: '_AccessMedicalDevice',
  display: 'AccessMedicalDevice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_AdministrationMedicalDevice: Coding = {
  code: '_AdministrationMedicalDevice',
  display: 'AdministrationMedicalDevice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_ClinicalDrug: Coding = {
  code: '_ClinicalDrug',
  display: 'ClinicalDrug',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_ContainerEntityType: Coding = {
  code: '_ContainerEntityType',
  display: 'ContainerEntityType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_DrugEntity: Coding = {
  code: '_DrugEntity',
  display: 'DrugEntity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_IndividualPackageEntityType: Coding = {
  code: '_IndividualPackageEntityType',
  display: 'IndividualPackageEntityType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_InjectionMedicalDevice: Coding = {
  code: '_InjectionMedicalDevice',
  display: 'InjectionMedicalDevice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_MaterialEntityClassType: Coding = {
  code: '_MaterialEntityClassType',
  display: 'MaterialEntityClassType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_MedicalDevice: Coding = {
  code: '_MedicalDevice',
  display: 'MedicalDevice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_MultiUseContainerEntityType: Coding = {
  code: '_MultiUseContainerEntityType',
  display: 'MultiUseContainerEntityType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_NonDrugAgentEntity: Coding = {
  code: '_NonDrugAgentEntity',
  display: 'NonDrugAgentEntity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_NonRigidContainerEntityType: Coding = {
  code: '_NonRigidContainerEntityType',
  display: 'NonRigidContainerEntityType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_OrganizationEntityType: Coding = {
  code: '_OrganizationEntityType',
  display: 'OrganizationEntityType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_PlaceEntityType: Coding = {
  code: '_PlaceEntityType',
  display: 'PlaceEntityType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_ResourceGroupEntityType: Coding = {
  code: '_ResourceGroupEntityType',
  display: 'ResourceGroupEntityType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_RigidContainerEntityType: Coding = {
  code: '_RigidContainerEntityType',
  display: 'RigidContainerEntityType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_SpecimenAdditiveEntity: Coding = {
  code: '_SpecimenAdditiveEntity',
  display: 'SpecimenAdditiveEntity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_ACDSolutionA: Coding = {
  code: 'ACDA',
  display: 'ACD Solution A',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_ACDSolutionB: Coding = {
  code: 'ACDB',
  display: 'ACD Solution B',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_AceticAcid: Coding = {
  code: 'ACET',
  display: 'Acetic Acid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_AutoInjector: Coding = {
  code: 'AINJ',
  display: 'AutoInjector',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_AmiesTransportMedium: Coding = {
  code: 'AMIES',
  display: 'Amies transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Ampule: Coding = {
  code: 'AMP',
  display: 'Ampule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Applicator: Coding = {
  code: 'APLCTR',
  display: 'Applicator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_BacterialTransportMedium: Coding = {
  code: 'BACTM',
  display: 'Bacterial Transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Bag: Coding = {
  code: 'BAG',
  display: 'Bag',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_BedLocation: Coding = {
  code: 'BED',
  display: 'Bed Location',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Buffered10PercentFormalin: Coding = {
  code: 'BF10',
  display: 'Buffered 10% formalin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_BuildingLocation: Coding = {
  code: 'BLDG',
  display: 'Building Location',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_BloodProduct: Coding = {
  code: 'BLDPRD',
  display: 'Blood Product',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_BlisterPack: Coding = {
  code: 'BLSTRPK',
  display: 'Blister Pack',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_BoricAcid: Coding = {
  code: 'BOR',
  display: 'Boric Acid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Bottle: Coding = {
  code: 'BOT',
  display: 'Bottle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_AmberBottle: Coding = {
  code: 'BOTA',
  display: 'Amber Bottle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_DropperBottle: Coding = {
  code: 'BOTD',
  display: 'Dropper Bottle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_GlassBottle: Coding = {
  code: 'BOTG',
  display: 'Glass Bottle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_PlasticBottle: Coding = {
  code: 'BOTP',
  display: 'Plastic Bottle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_PolyethyleneBottle: Coding = {
  code: 'BOTPLY',
  display: 'Polyethylene Bottle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_BouinQuoteSSolution: Coding = {
  code: 'BOUIN',
  display: "Bouin's solution",
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Box: Coding = {
  code: 'BOX',
  display: 'Box',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_BufferedSkimMilk: Coding = {
  code: 'BSKM',
  display: 'Buffered skim milk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_VAL32PercentCitrate: Coding = {
  code: 'C32',
  display: '3.2% Citrate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_VAL38PercentCitrate: Coding = {
  code: 'C38',
  display: '3.8% Citrate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Can: Coding = {
  code: 'CAN',
  display: 'Can',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Card: Coding = {
  code: 'CARD',
  display: 'Card',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_CarsonQuoteSModified10PercentFormalin: Coding = {
  code: 'CARS',
  display: "Carson's Modified 10% formalin",
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Cartridge: Coding = {
  code: 'CART',
  display: 'Cartridge',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_CaryBlairMedium: Coding = {
  code: 'CARY',
  display: 'Cary Blair Medium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_ChlamydiaTransportMedium: Coding = {
  code: 'CHLTM',
  display: 'Chlamydia transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Canister: Coding = {
  code: 'CNSTR',
  display: 'Canister',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_CompliancePackage: Coding = {
  code: 'COMPPKG',
  display: 'Compliance Package',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_CTAD: Coding = {
  code: 'CTAD',
  display: 'CTAD',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_DialPack: Coding = {
  code: 'DIALPK',
  display: 'Dial Pack',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Disk: Coding = {
  code: 'DISK',
  display: 'Disk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Dosette: Coding = {
  code: 'DOSET',
  display: 'Dosette',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Diskus: Coding = {
  code: 'DSKS',
  display: 'Diskus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Diskhaler: Coding = {
  code: 'DSKUNH',
  display: 'Diskhaler',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_PotassiumKEDTA15Percent: Coding = {
  code: 'EDTK15',
  display: 'Potassium/K EDTA 15%',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_PotassiumKEDTA75Percent: Coding = {
  code: 'EDTK75',
  display: 'Potassium/K EDTA 7.5%',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_SodiumNaEDTA: Coding = {
  code: 'EDTN',
  display: 'Sodium/Na EDTA',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_EntericBacteriaTransportMedium: Coding = {
  code: 'ENT',
  display: 'Enteric bacteria transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_VAL10PercentFormalin: Coding = {
  code: 'F10',
  display: '10% Formalin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_ThrombinNIHSoybeanTrypsinInhibitor: Coding = {
  code: 'FDP',
  display: 'Thrombin NIH; soybean trypsin inhibitor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_SodiumFluoride10mg: Coding = {
  code: 'FL10',
  display: 'Sodium Fluoride, 10mg',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_SodiumFluoride100mg: Coding = {
  code: 'FL100',
  display: 'Sodium Fluoride, 100mg',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_FloorLocation: Coding = {
  code: 'FLOOR',
  display: 'Floor Location',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_VAL6NHCL: Coding = {
  code: 'HCL6',
  display: '6N HCL',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_AmmoniumHeparin: Coding = {
  code: 'HEPA',
  display: 'Ammonium heparin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_LithiumLiHeparin: Coding = {
  code: 'HEPL',
  display: 'Lithium/Li Heparin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_SodiumNaHeparin: Coding = {
  code: 'HEPN',
  display: 'Sodium/Na Heparin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Household: Coding = {
  code: 'HHOLD',
  display: 'household',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_NitricAcid: Coding = {
  code: 'HNO3',
  display: 'Nitric Acid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_IntraArterialLine: Coding = {
  code: 'IALINE',
  display: 'Intra-arterial Line',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Inhaler: Coding = {
  code: 'INH',
  display: 'Inhaler',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_IntraveneousLine: Coding = {
  code: 'IVLINE',
  display: 'Intraveneous Line',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Jar: Coding = {
  code: 'JAR',
  display: 'Jar',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_JonesKendrickMedium: Coding = {
  code: 'JKM',
  display: 'Jones Kendrick Medium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Jug: Coding = {
  code: 'JUG',
  display: 'Jug',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_KarnovskyQuoteSFixative: Coding = {
  code: 'KARN',
  display: "Karnovsky's fixative",
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Kit: Coding = {
  code: 'KIT',
  display: 'Kit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_PotassiumOxalate: Coding = {
  code: 'KOX',
  display: 'Potassium Oxalate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_LithiumIodoacetate: Coding = {
  code: 'LIA',
  display: 'Lithium iodoacetate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Line: Coding = {
  code: 'LINE',
  display: 'Line',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_M4: Coding = {
  code: 'M4',
  display: 'M4',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_M4RT: Coding = {
  code: 'M4RT',
  display: 'M4-RT',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_M5: Coding = {
  code: 'M5',
  display: 'M5',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_MichelQuoteSTransportMedium: Coding = {
  code: 'MICHTM',
  display: "Michel's transport medium",
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Minim: Coding = {
  code: 'MINIM',
  display: 'Minim',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_MMDTransportMedium: Coding = {
  code: 'MMDTM',
  display: 'MMD transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_SodiumFluoride: Coding = {
  code: 'NAF',
  display: 'Sodium Fluoride',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_NationEntityType: Coding = {
  code: 'NAT',
  display: 'NationEntityType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Egg: Coding = {
  code: 'NDA01',
  display: 'egg',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Fish: Coding = {
  code: 'NDA02',
  display: 'fish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Lactose: Coding = {
  code: 'NDA03',
  display: 'lactose',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Peanut: Coding = {
  code: 'NDA04',
  display: 'peanut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Soy: Coding = {
  code: 'NDA05',
  display: 'soy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Sulfites: Coding = {
  code: 'NDA06',
  display: 'sulfites',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_WheatOrGluten: Coding = {
  code: 'NDA07',
  display: 'wheat or gluten',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Isocyanates: Coding = {
  code: 'NDA08',
  display: 'isocyanates',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Solvents: Coding = {
  code: 'NDA09',
  display: 'solvents',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Oils: Coding = {
  code: 'NDA10',
  display: 'oils',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Venoms: Coding = {
  code: 'NDA11',
  display: 'venoms',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Latex: Coding = {
  code: 'NDA12',
  display: 'latex',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Shellfish: Coding = {
  code: 'NDA13',
  display: 'shellfish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Strawberries: Coding = {
  code: 'NDA14',
  display: 'strawberries',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Tomatoes: Coding = {
  code: 'NDA15',
  display: 'tomatoes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Dust: Coding = {
  code: 'NDA16',
  display: 'dust',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_DustMites: Coding = {
  code: 'NDA17',
  display: 'dust mites',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Nebuamp: Coding = {
  code: 'NEBAMP',
  display: 'Nebuamp',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_None: Coding = {
  code: 'NONE',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Ovule: Coding = {
  code: 'OVUL',
  display: 'Ovule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Packet: Coding = {
  code: 'PACKT',
  display: 'Packet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_PageQuoteSSaline: Coding = {
  code: 'PAGE',
  display: "Page's Saline",
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Pouch: Coding = {
  code: 'PCH',
  display: 'Pouch',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Pen: Coding = {
  code: 'PEN',
  display: 'Pen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Phenol: Coding = {
  code: 'PHENOL',
  display: 'Phenol',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Package: Coding = {
  code: 'PKG',
  display: 'Package',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Pump: Coding = {
  code: 'PMP',
  display: 'Pump',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_PractitionerGroup: Coding = {
  code: 'PRAC',
  display: 'PractitionerGroup',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Polyvinylalcohol: Coding = {
  code: 'PVA',
  display: 'Polyvinylalcohol',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_ReligiousInstitution: Coding = {
  code: 'RELIG',
  display: 'religious institution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_ReaganLoweMedium: Coding = {
  code: 'RLM',
  display: 'Reagan Lowe Medium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_RoomLocation: Coding = {
  code: 'ROOM',
  display: 'Room Location',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Sachet: Coding = {
  code: 'SACH',
  display: 'Sachet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_SiliceousEarth: Coding = {
  code: 'SILICA',
  display: 'Siliceous earth',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_SodiumPolyanetholSulfonate035PercentIn085PercentSodiumChloride: Coding = {
  code: 'SPS',
  display: 'Sodium polyanethol sulfonate 0.35% in 0.85% sodium chloride',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_SerumSeparatorTube: Coding = {
  code: 'SST',
  display: 'Serum Separator Tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Strip: Coding = {
  code: 'STRIP',
  display: 'Strip',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_StuartTransportMedium: Coding = {
  code: 'STUTM',
  display: 'Stuart transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Syringe: Coding = {
  code: 'SYR',
  display: 'Syringe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_System: Coding = {
  code: 'SYSTM',
  display: 'System',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Thrombin: Coding = {
  code: 'THROM',
  display: 'Thrombin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Thymol: Coding = {
  code: 'THYMOL',
  display: 'Thymol',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_ThyoglycolateBroth: Coding = {
  code: 'THYO',
  display: 'Thyoglycolate broth',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Tin: Coding = {
  code: 'TIN',
  display: 'Tin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Toluene: Coding = {
  code: 'TOLU',
  display: 'Toluene',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Turbuhaler: Coding = {
  code: 'TRBINH',
  display: 'Turbuhaler',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Tub: Coding = {
  code: 'TUB',
  display: 'Tub',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Tube: Coding = {
  code: 'TUBE',
  display: 'Tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_UreaplasmaTransportMedium: Coding = {
  code: 'URETM',
  display: 'Ureaplasma transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Vaccine: Coding = {
  code: 'VCCNE',
  display: 'Vaccine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_Vial: Coding = {
  code: 'VIAL',
  display: 'Vial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_ViralTransportMedium: Coding = {
  code: 'VIRTM',
  display: 'Viral Transport medium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_BufferedCitrate: Coding = {
  code: 'WEST',
  display: 'Buffered Citrate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
const v3_EntityCode_WingLocation: Coding = {
  code: 'WING',
  display: 'Wing Location',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityCode',
};
/**
 *   OpenIssue:
 * Missing description.
 */
export const V3EntityCode = {
  /**
   * A device used to allow access to a part of a body
   */
  AccessMedicalDevice: v3_EntityCode_AccessMedicalDevice,
  /**
   * A device intended to administer a substance to a subject
   */
  AdministrationMedicalDevice: v3_EntityCode_AdministrationMedicalDevice,
  /**
   * Any substance or mixture of substances manufactured, sold or represented for use in: (a) the diagnosis, treatment, mitigation or prevention of a disease, disorder, abnormal physical state, or its symptoms, in human beings or animals; (b) restoring, correcting or modifying organic functions in human beings or animals.
   */
  ClinicalDrug: v3_EntityCode_ClinicalDrug,
  /**
   * Material intended to hold another material for purpose of storage or transport.
   */
  ContainerEntityType: v3_EntityCode_ContainerEntityType,
  /**
   * A substance whose therapeutic effect is produced by chemical action within the body.
   */
  DrugEntity: v3_EntityCode_DrugEntity,
  /**
   * Container intended to contain sufficient material for only one use.
   */
  IndividualPackageEntityType: v3_EntityCode_IndividualPackageEntityType,
  /**
   * A device intended to administer liquid into a subject via a
   */
  InjectionMedicalDevice: v3_EntityCode_InjectionMedicalDevice,
  /**
   * Types of Material for EntityClass "MAT"
   */
  MaterialEntityClassType: v3_EntityCode_MaterialEntityClassType,
  /**
   * A device with direct or indirect therapeutic purpose.  Values for EntityCode when EntityClass = "DEV"
   */
  MedicalDevice: v3_EntityCode_MedicalDevice,
  /**
   * A container intended to contain sufficient material for more than one use.  (I.e. Material is intended to be removed from the container at more than one discrete time period.)
   */
  MultiUseContainerEntityType: v3_EntityCode_MultiUseContainerEntityType,
  /**
   * Indicates types of allergy and intolerance agents which are non-drugs.  (E.g. foods, latex, etc.)
   */
  NonDrugAgentEntity: v3_EntityCode_NonDrugAgentEntity,
  /**
   * A container having dimensions that adjust somewhat based on the amount and shape of the material placed within it.
   */
  NonRigidContainerEntityType: v3_EntityCode_NonRigidContainerEntityType,
  /**
   * Further classifies entities of classCode ORG.
   */
  OrganizationEntityType: v3_EntityCode_OrganizationEntityType,
  /**
   * Types of places for EntityClass "PLC"
   */
  PlaceEntityType: v3_EntityCode_PlaceEntityType,
  /**
   * Codes to characterize a Resource Group using categories that typify its membership and/or function
   *
   *                         .
   *
   *
   *                            Example: PractitionerGroup
   */
  ResourceGroupEntityType: v3_EntityCode_ResourceGroupEntityType,
  /**
   * A container having a fixed and inflexible dimensions and volume
   */
  RigidContainerEntityType: v3_EntityCode_RigidContainerEntityType,
  /**
   * Set of codes related to specimen additives
   */
  SpecimenAdditiveEntity: v3_EntityCode_SpecimenAdditiveEntity,
  /**
   * ACD Solution A of trisodium citrate, 22.0g/L; citric acid, 8.0 g/L; and dextrose 24.5 g/L. Used in Blood banking and histocompatibilty testing
   */
  ACDSolutionA: v3_EntityCode_ACDSolutionA,
  /**
   * ACD Solution B of trisodium citrate, 13.2g/L; citric acid, 4.8 g/L; and dextrose 14.7 g/L. Used in Blood banking and histocompatibilty testing.
   */
  ACDSolutionB: v3_EntityCode_ACDSolutionB,
  /**
   * 50% V/V acetic acid in water.  Used as  a urine preservative
   */
  AceticAcid: v3_EntityCode_AceticAcid,
  /**
   * Automatically injects medication.
   */
  AutoInjector: v3_EntityCode_AutoInjector,
  /**
   * Sodium Chloride 3.0g, Potassium Chloride 0.2g, Calcium Chloride 0.1g, Magnesium Chloride 0.1g, Monopotassium Phosphate 0.2g, Disodium Phosphate 1.15g, Sodium Thiogly collate 1.0g, Distilled Water 1 liter
   */
  AmiesTransportMedium: v3_EntityCode_AmiesTransportMedium,
  /**
   * A small sealed glass container that holds a measured amount of a medicinal substance.
   */
  Ampule: v3_EntityCode_Ampule,
  /**
   * A device used to apply a liquid or powder to a surface.
   */
  Applicator: v3_EntityCode_Applicator,
  /**
   * Any medium used to maintain bacterial viability (e.g. Stuart's, Cary-Blair, Amies)
   */
  BacterialTransportMedium: v3_EntityCode_BacterialTransportMedium,
  /**
   * A pouched or pendulous container.
   */
  Bag: v3_EntityCode_Bag,
  /**
   * The location of a bed
   */
  BedLocation: v3_EntityCode_BedLocation,
  /**
   * Formaldehyde 4% w/v; methyl alcohol 1% w/v; phosphate buffering salts. Tissue preservative
   */
  Buffered10PercentFormalin: v3_EntityCode_Buffered10PercentFormalin,
  /**
   * The location of a building
   */
  BuildingLocation: v3_EntityCode_BuildingLocation,
  /**
   * A manufactured product that is produced from the raw blood oi a donor with the intention of using it in a recipient transfusion.
   */
  BloodProduct: v3_EntityCode_BloodProduct,
  /**
   * A bubblepack.  Medications sealed individually, separated into doses.
   */
  BlisterPack: v3_EntityCode_BlisterPack,
  /**
   * Powdered boric acid (usually 10 g) added to 24-hour urine collections as a preservative.
   */
  BoricAcid: v3_EntityCode_BoricAcid,
  /**
   * A container, typically rounded, either glass or plastic with a narrow neck and capable of storing liquid.
   */
  Bottle: v3_EntityCode_Bottle,
  /**
   * A bottle of yellow to brown color.  Used to store light-sensitive materials
   */
  AmberBottle: v3_EntityCode_AmberBottle,
  /**
   * A bottle with a cap designed to release the contained liquid in droplets of a specific size.
   */
  DropperBottle: v3_EntityCode_DropperBottle,
  /**
   * A bottle made of glass
   */
  GlassBottle: v3_EntityCode_GlassBottle,
  /**
   * A bottle made of plastic
   */
  PlasticBottle: v3_EntityCode_PlasticBottle,
  /**
   * A bottle made of polyethylene
   */
  PolyethyleneBottle: v3_EntityCode_PolyethyleneBottle,
  /**
   * Picric acid, saturated aqueous solution (750.0 ml), 37-40% formalin (250.0 ml), glacial acetic acid (50.0 ml). Tissue preservative.
   */
  BouinQuoteSSolution: v3_EntityCode_BouinQuoteSSolution,
  /**
   * A 6-sided container commonly made from paper or cardboard used for solid forms.
   */
  Box: v3_EntityCode_Box,
  /**
   * 50% skim milk in 0.01 M phosphate-buffered saline.  Maintain virus viability
   */
  BufferedSkimMilk: v3_EntityCode_BufferedSkimMilk,
  /**
   * A 3.2% solution of Sodium Citrate in water.  Used as a blood preservative
   */
  VAL32PercentCitrate: v3_EntityCode_VAL32PercentCitrate,
  /**
   * A 3.8% solution of Sodium Citrate in water. Used as a blood preservative
   */
  VAL38PercentCitrate: v3_EntityCode_VAL38PercentCitrate,
  /**
   * A metal container in which a material is hermetically sealed to enable storage over long periods.
   */
  Can: v3_EntityCode_Can,
  /**
   * A bubble pack card.  Multiple individual/separated doses.
   */
  Card: v3_EntityCode_Card,
  /**
   * A modification of buffered 10% formalin used as a general tissue preservative.
   */
  CarsonQuoteSModified10PercentFormalin: v3_EntityCode_CarsonQuoteSModified10PercentFormalin,
  /**
   * A sealed container of liquid or powder intended to be loaded into a device.
   */
  Cartridge: v3_EntityCode_Cartridge,
  /**
   * Sodium Thioglycollate 1.5 g, Disodium Hydrogen Phosphate 1.1 g, Sodium Chloride 5.0 g, Calcium Chloride 0.09 g, Agar 5.0 g, per Liter of Water
   */
  CaryBlairMedium: v3_EntityCode_CaryBlairMedium,
  /**
   * Any of a number of non-nutritive buffered media used to maintain Chlamydia viability during transportation to the laboratory
   */
  ChlamydiaTransportMedium: v3_EntityCode_ChlamydiaTransportMedium,
  /**
   * A pressurized metal container holding a substance released as a spray or aerosol.
   */
  Canister: v3_EntityCode_Canister,
  /**
   * A container intended to contain sufficient material for more than one use, but grouped or organized to provide individual access to sufficient material for a single use.  Often used to ensure that the proper type and amount of material is consumed/expended for each use.
   */
  CompliancePackage: v3_EntityCode_CompliancePackage,
  /**
   * Buffered tri-sodium citrate solution with theophylline, adenosine and dipyridamole
   */
  CTAD: v3_EntityCode_CTAD,
  /**
   * Rotatable dispenser.  Eg. Birth control package.
   */
  DialPack: v3_EntityCode_DialPack,
  /**
   * Object that is thin, flat, and circular.  Doses of medication often contained in bubbles on the disk.
   */
  Disk: v3_EntityCode_Disk,
  /**
   * Special packaging that will help patients take their medications on a regular basis.
   */
  Dosette: v3_EntityCode_Dosette,
  /**
   * The device used to inhale the doses of medication contained in the disk form.
   */
  Diskus: v3_EntityCode_Diskus,
  /**
   * The device used to inhale the doses of medication contained in the disk form.
   */
  Diskhaler: v3_EntityCode_Diskhaler,
  /**
   * Potassium EDTA 15% solution in water
   */
  PotassiumKEDTA15Percent: v3_EntityCode_PotassiumKEDTA15Percent,
  /**
   * Potassium EDTA 7.5% solution in water
   */
  PotassiumKEDTA75Percent: v3_EntityCode_PotassiumKEDTA75Percent,
  /**
   * Sodium fluoride and Disodium EDTA
   */
  SodiumNaEDTA: v3_EntityCode_SodiumNaEDTA,
  /**
   * Any of a number of non-nutritive buffered media used to maintain enteric bacterial viability during transportation to the laboratory
   */
  EntericBacteriaTransportMedium: v3_EntityCode_EntericBacteriaTransportMedium,
  /**
   * A 10% v/v solution in water of formalin( a 37% solution of formaldehyde and water).  Used for tissue preservation.
   */
  VAL10PercentFormalin: v3_EntityCode_VAL10PercentFormalin,
  /**
   * Thrombin plus soybean trypsin inhibitor.  For use in identifying fibrn degredation products.
   */
  ThrombinNIHSoybeanTrypsinInhibitor: v3_EntityCode_ThrombinNIHSoybeanTrypsinInhibitor,
  /**
   * Sodium fluoride, 10mg added as a urine preservative.
   */
  SodiumFluoride10mg: v3_EntityCode_SodiumFluoride10mg,
  /**
   * Sodium fluoride, 100mg added as a urine preservative.
   */
  SodiumFluoride100mg: v3_EntityCode_SodiumFluoride100mg,
  /**
   * The location of a floor of a building
   */
  FloorLocation: v3_EntityCode_FloorLocation,
  /**
   * A solution of HCl containing 6moles of hydrogen ion/L. Used as a Urine Preservative.
   */
  VAL6NHCL: v3_EntityCode_VAL6NHCL,
  /**
   * Ammonium heparin
   */
  AmmoniumHeparin: v3_EntityCode_AmmoniumHeparin,
  /**
   * Lithium heparin salt
   */
  LithiumLiHeparin: v3_EntityCode_LithiumLiHeparin,
  /**
   * Sodium heparin salt
   */
  SodiumNaHeparin: v3_EntityCode_SodiumNaHeparin,
  /**
   * The group of persons who occupy a single housing unit.
   */
  Household: v3_EntityCode_Household,
  /**
   * 6N Nitric acid used to preserve urine for heavy metal analysis.
   */
  NitricAcid: v3_EntityCode_NitricAcid,
  /**
   * A line used to administer a substance into an artery
   */
  IntraArterialLine: v3_EntityCode_IntraArterialLine,
  /**
   * A small device used for inhaling medicine in the form of a vapour or gas in order to ease a respiratory condition such as asthma or to relieve nasal congestion.
   */
  Inhaler: v3_EntityCode_Inhaler,
  /**
   * A line used to administer a substance into a vein
   */
  IntraveneousLine: v3_EntityCode_IntraveneousLine,
  /**
   * A container of glass, earthenware, plastic, etc.  Top of the container has a diameter of similar size to the diameter of the container as a whole
   */
  Jar: v3_EntityCode_Jar,
  /**
   * A transport medium formulated to maintain Bordetella pertussis viability.
   */
  JonesKendrickMedium: v3_EntityCode_JonesKendrickMedium,
  /**
   * A deep vessel  for holding liquids, with a handle and often with a spout or lip shape for pouring.
   */
  Jug: v3_EntityCode_Jug,
  /**
   * 5% Glutaraldehyde, 4% Formaldehyde in 0.08M buffer. Tissue preservation
   */
  KarnovskyQuoteSFixative: v3_EntityCode_KarnovskyQuoteSFixative,
  /**
   * A container for a diverse collection of products intended to be used together for some purpose (e.g. Medicinal kits often contain a syringe, a needle and the injectable medication).
   */
  Kit: v3_EntityCode_Kit,
  /**
   * Potassium oxalate and sodium fluoride in a 1.25:1 ratio
   */
  PotassiumOxalate: v3_EntityCode_PotassiumOxalate,
  /**
   * Iodoacetate lithium salt
   */
  LithiumIodoacetate: v3_EntityCode_LithiumIodoacetate,
  /**
   * A hollow tube used to administer a substance into a vein, artery or body cavity
   */
  Line: v3_EntityCode_Line,
  /**
   * Modified Hank's balanced salt solution supplemented with bovine serum albumin, gelatin, sucrose and glutamic acid. It is buffered to pH 7.3+ or - 0.2 with HEPES buffer. Phenol red is used to indicate pH. Vancomycin, Amphotericin B and Colistin are used to
   */
  M4: v3_EntityCode_M4,
  /**
   * Modified Hank's balanced salt solution supplemented with bovine serum albumin, gelatin, sucrose and glutamic acid. It is buffered to pH 7.3+ or - 0.2 with Hepes buffer. Phenol red is used to indicate pH. Gentamicin and amphotericin B are used to inhibit c
   */
  M4RT: v3_EntityCode_M4RT,
  /**
   * Modified Hank's balanced salt solution supplemented with protein stabilizers, sucrose and glutamic acid. It is buffered to pH 7.3+ or - 0.2 with Hepes buffer. Phenol red is used to indicate pH. Vancomycin, Amphotericin B and Colistin are used to inhibit c
   */
  M5: v3_EntityCode_M5,
  /**
   * 1M potassium citrate, pH 7.0 2.5 ml, 0.1M magnesium sulfate 5.0 ml, 0.1M N-ethyl malemide  5.0 ml, dH2O 87.5 ml, ammonium sulfate 55gm. Preserve antigens for Immunofluorescence procedures
   */
  MichelQuoteSTransportMedium: v3_EntityCode_MichelQuoteSTransportMedium,
  /**
   * Individually dosed ophthalmic solution.  One time eye dropper dispenser.
   */
  Minim: v3_EntityCode_Minim,
  /**
   * A buffered medium with ammonium sulfate added to preserve antigens for Immunofluorescence procedures
   */
  MMDTransportMedium: v3_EntityCode_MMDTransportMedium,
  /**
   * Sodium fluoride
   */
  SodiumFluoride: v3_EntityCode_SodiumFluoride,
  /**
   * Codes identifying nation states.  Allows for finer grained specification of Entity with classcode <= NAT
   *
   *
   *                            Example:ISO3166 country codes.
   */
  NationEntityType: v3_EntityCode_NationEntityType,
  /**
   * egg
   */
  Egg: v3_EntityCode_Egg,
  /**
   * fish
   */
  Fish: v3_EntityCode_Fish,
  /**
   * lactose
   */
  Lactose: v3_EntityCode_Lactose,
  /**
   * peanut
   */
  Peanut: v3_EntityCode_Peanut,
  /**
   * soy
   */
  Soy: v3_EntityCode_Soy,
  /**
   * sulfites
   */
  Sulfites: v3_EntityCode_Sulfites,
  /**
   * wheat or gluten
   */
  WheatOrGluten: v3_EntityCode_WheatOrGluten,
  /**
   * isocyanates
   */
  Isocyanates: v3_EntityCode_Isocyanates,
  /**
   * solvents
   */
  Solvents: v3_EntityCode_Solvents,
  /**
   * oils
   */
  Oils: v3_EntityCode_Oils,
  /**
   * venoms
   */
  Venoms: v3_EntityCode_Venoms,
  /**
   * latex
   */
  Latex: v3_EntityCode_Latex,
  /**
   * shellfish
   */
  Shellfish: v3_EntityCode_Shellfish,
  /**
   * strawberries
   */
  Strawberries: v3_EntityCode_Strawberries,
  /**
   * tomatoes
   */
  Tomatoes: v3_EntityCode_Tomatoes,
  /**
   * dust
   */
  Dust: v3_EntityCode_Dust,
  /**
   * dust mites
   */
  DustMites: v3_EntityCode_DustMites,
  /**
   * Individually dosed inhalation solution.
   */
  Nebuamp: v3_EntityCode_Nebuamp,
  /**
   * No additive. Specifically identifes the specimen as having no additives.
   */
  None: v3_EntityCode_None,
  /**
   * A container either glass or plastic and a narrow neck, for storing liquid.
   */
  Ovule: v3_EntityCode_Ovule,
  /**
   * A paper
   */
  Packet: v3_EntityCode_Packet,
  /**
   * 0.12 g NaCl, 0.004 g MgSO, 0.004 g, CaCl, 0.142 g Na2HPO4 and 0.136 g KH2PO4 per liter of distilled water. Maintain Acanthaoemba viability.
   */
  PageQuoteSSaline: v3_EntityCode_PageQuoteSSaline,
  /**
   * A small bag or container made of a soft material.
   */
  Pouch: v3_EntityCode_Pouch,
  /**
   * A device which can contain a cartridge for injection purposes.  Eg. Insulin pen.
   */
  Pen: v3_EntityCode_Pen,
  /**
   * Phenol. Urine preservative
   */
  Phenol: v3_EntityCode_Phenol,
  /**
   * A material intended to hold other materials for purposes of storage or transportation
   */
  Package: v3_EntityCode_Package,
  /**
   * A device that is used to raise, compress, or transfer liquids or gases and is operated by a piston or similar mechanism.
   */
  Pump: v3_EntityCode_Pump,
  /**
   * PractitionerGroup
   */
  PractitionerGroup: v3_EntityCode_PractitionerGroup,
  /**
   * Polyvinyl alcohol
   */
  Polyvinylalcohol: v3_EntityCode_Polyvinylalcohol,
  /**
   * An organization that provides religious rites of worship.
   */
  ReligiousInstitution: v3_EntityCode_ReligiousInstitution,
  /**
   * A transport medium formulated to maintain Bordetella pertussis viability.
   */
  ReaganLoweMedium: v3_EntityCode_ReaganLoweMedium,
  /**
   * The location of a room
   */
  RoomLocation: v3_EntityCode_RoomLocation,
  /**
   * A small bag or packet containing a small portion of a substance.
   */
  Sachet: v3_EntityCode_Sachet,
  /**
   * Diatomaceous earth. For glucose determination blood samples
   */
  SiliceousEarth: v3_EntityCode_SiliceousEarth,
  /**
   * Sodium polyanethol sulfonate in saline. Anticomplementary and antiphagocytic properties. Used in blood culture collection.
   */
  SodiumPolyanetholSulfonate035PercentIn085PercentSodiumChloride: v3_EntityCode_SodiumPolyanetholSulfonate035PercentIn085PercentSodiumChloride,
  /**
   * Polymer separator gel with clot activator
   */
  SerumSeparatorTube: v3_EntityCode_SerumSeparatorTube,
  /**
   * A continuous strip of plastic sectioned into individual pouches, each one containing the quantity of 1 or more medications intended to be administered at a specific time
   */
  Strip: v3_EntityCode_Strip,
  /**
   * Sodium Glycerophosphate 10.0g, Calcium Chloride 0.1g, Mercaptoacetic Acid 1.0ml, Distilled Water 1 liter
   */
  StuartTransportMedium: v3_EntityCode_StuartTransportMedium,
  /**
   * A barrel with a plunger.
   */
  Syringe: v3_EntityCode_Syringe,
  /**
   * A kit in which the components are interconnected.
   */
  System: v3_EntityCode_System,
  /**
   * Thrombin. Accelerates clotting.
   */
  Thrombin: v3_EntityCode_Thrombin,
  /**
   * 2-Isopropyl-5-methyl phenol. A preservative for 24 Hr Urine samples
   */
  Thymol: v3_EntityCode_Thymol,
  /**
   * A nutritive medium with a reducing agent  (sodium thioglycolate) which, due to a chemical reaction, removes oxygen from the broth.
   */
  ThyoglycolateBroth: v3_EntityCode_ThyoglycolateBroth,
  /**
   * A lidded container made of thin sheet metal.
   */
  Tin: v3_EntityCode_Tin,
  /**
   * Also known as Methylbenzene; Toluol; Phenylmethane. A preservative for 24 Hr Urine samples
   */
  Toluene: v3_EntityCode_Toluene,
  /**
   * Asthma medication delivery device.
   */
  Turbuhaler: v3_EntityCode_Turbuhaler,
  /**
   * An open flat bottomed round container.
   */
  Tub: v3_EntityCode_Tub,
  /**
   * A long hollow rigid or flexible cylinder.  Material is extruded by squeezing the container.
   */
  Tube: v3_EntityCode_Tube,
  /**
   * A buffered salt solution with antifungal agents added for the collection and transport of Ureaplasma specimens.
   */
  UreaplasmaTransportMedium: v3_EntityCode_UreaplasmaTransportMedium,
  /**
   * A Type of medicine that creates an immune protection without the recipient experiencing the disease.
   */
  Vaccine: v3_EntityCode_Vaccine,
  /**
   * A small cylindrical glass for holding liquid medicines.
   */
  Vial: v3_EntityCode_Vial,
  /**
   * Sucrose 74.6g, Potassium hydrogenphosphate 0.52g, L-glutamic acid 0.72g, Bovine serum albumin 5.0g, Gentamicin 50mg, Potassium dihydrogenphosphate 1.25g, L-15 medium 9.9L, Water to 10L. Maintain Virus viability.
   */
  ViralTransportMedium: v3_EntityCode_ViralTransportMedium,
  /**
   * 3.8% Citrate buffered to a pH of 5.5 for Westergren Sedimentation Rate
   */
  BufferedCitrate: v3_EntityCode_BufferedCitrate,
  /**
   * The location of a wing of a building (e.g. East Wing).  The same room number for the same floor number can be distinguished by wing number in some situations
   */
  WingLocation: v3_EntityCode_WingLocation,
};
const v3_EntityDeterminer_SpecificGroup: Coding = {
  code: 'GROUP',
  display: 'specific group',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityDeterminer',
};
const v3_EntityDeterminer_DescribedGroup: Coding = {
  code: 'GROUPKIND',
  display: 'described group',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityDeterminer',
};
const v3_EntityDeterminer_Specific: Coding = {
  code: 'INSTANCE',
  display: 'specific',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityDeterminer',
};
const v3_EntityDeterminer_Described: Coding = {
  code: 'KIND',
  display: 'described',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityDeterminer',
};
const v3_EntityDeterminer_DescribedQuantified: Coding = {
  code: 'QUANTIFIED_KIND',
  display: 'described quantified',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityDeterminer',
};
/**
 *  EntityDeterminer in natural language grammar is the class of words that comprises articles, demonstrative pronouns, and quantifiers. In the RIM, determiner is a structural code in the Entity class to distinguish whether any given Entity object stands for some, any one, or a specific thing.
 */
export const V3EntityDeterminer = {
  /**
   * A determiner that specifies that the Entity object represents a particular collection of physical things (as opposed to a universal, kind, or class of physical thing).  While the collection may resolve to having only a single individual (or even no individuals), the potential should exist for it to cover multiple individuals.
   */
  SpecificGroup: v3_EntityDeterminer_SpecificGroup,
  /**
   * A determiner that specifies that the Entity object represents a universal, kind or class of collections physical things.  While the collection may resolve to having only a single individual (or even no individuals), the potential should exist for it to cover multiple individuals.
   */
  DescribedGroup: v3_EntityDeterminer_DescribedGroup,
  /**
   * Description:A determiner that specifies that the Entity object represents a particular physical thing (as opposed to a universal, kind, or class of physical thing).
   *
   *
   *                            Discussion: It does not matter whether an INSTANCE still exists as a whole at the point in time (or process) when we mention it, for example, a drug product lot is an INSTANCE even though it has been portioned out for retail purpose.
   */
  Specific: v3_EntityDeterminer_Specific,
  /**
   * Description:A determiner that specifies that the Entity object represents a universal, kind or class of physical thing (as opposed to a particular thing).
   */
  Described: v3_EntityDeterminer_Described,
  /**
   * The described quantified determiner indicates that the given Entity is taken as a general description of a specific amount of a thing. For example, QUANTIFIED_KIND of syringe (quantity = 3,) stands for exactly three syringes.
   */
  DescribedQuantified: v3_EntityDeterminer_DescribedQuantified,
};
/**
 *  The described determiner is used to indicate that the given Entity is taken as a general description of a kind of thing that can be taken in whole, in part, or in multiples.
 */
export const V3EntityDeterminerDetermined = {
  /**
   * A determiner that specifies that the Entity object represents a universal, kind or class of collections physical things.  While the collection may resolve to having only a single individual (or even no individuals), the potential should exist for it to cover multiple individuals.
   */
  DescribedGroup: v3_EntityDeterminer_DescribedGroup,
  /**
   * Description:A determiner that specifies that the Entity object represents a universal, kind or class of physical thing (as opposed to a particular thing).
   */
  Described: v3_EntityDeterminer_Described,
  /**
   * The described quantified determiner indicates that the given Entity is taken as a general description of a specific amount of a thing. For example, QUANTIFIED_KIND of syringe (quantity = 3,) stands for exactly three syringes.
   */
  DescribedQuantified: v3_EntityDeterminer_DescribedQuantified,
};
const v3_EntityHandling_AmbientTemperature: Coding = {
  code: 'AMB',
  display: 'Ambient Temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_BodyTemperature: Coding = {
  code: 'C37',
  display: 'Body Temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_CriticalAmbientTemperature: Coding = {
  code: 'CAMB',
  display: 'Critical Ambient temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_ProtectFromAir: Coding = {
  code: 'CATM',
  display: 'Protect from Air',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_CriticalFrozen: Coding = {
  code: 'CFRZ',
  display: 'Critical frozen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_CriticalRefrigeratedTemperature: Coding = {
  code: 'CREF',
  display: 'Critical refrigerated temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_DeepFrozen: Coding = {
  code: 'DFRZ',
  display: 'Deep Frozen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_Dry: Coding = {
  code: 'DRY',
  display: 'dry',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_Frozen: Coding = {
  code: 'FRZ',
  display: 'frozen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_MetalFree: Coding = {
  code: 'MTLF',
  display: 'Metal Free',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_Nitrogen: Coding = {
  code: 'NTR',
  display: 'nitrogen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_ProtectFromLight: Coding = {
  code: 'PRTL',
  display: 'Protect from Light',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_DoNotShake: Coding = {
  code: 'PSA',
  display: 'do not shake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_NoShock: Coding = {
  code: 'PSO',
  display: 'no shock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_RefrigeratedTemperature: Coding = {
  code: 'REF',
  display: 'Refrigerated temperature',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_ShakeBeforeUse: Coding = {
  code: 'SBU',
  display: 'Shake before use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_UltraFrozen: Coding = {
  code: 'UFRZ',
  display: 'Ultra frozen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
const v3_EntityHandling_Upright: Coding = {
  code: 'UPR',
  display: 'upright',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityHandling',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3EntityHandling = {
  /**
   * Keep at ambient temperature, 22 +/- 2C
   */
  AmbientTemperature: v3_EntityHandling_AmbientTemperature,
  /**
   * Critical to keep at body temperature 36-38C
   */
  BodyTemperature: v3_EntityHandling_BodyTemperature,
  /**
   * Critical ambient - must not be refrigerated or frozen.
   */
  CriticalAmbientTemperature: v3_EntityHandling_CriticalAmbientTemperature,
  /**
   * Critical. Do not expose to atmosphere.  Do not uncap.
   */
  ProtectFromAir: v3_EntityHandling_ProtectFromAir,
  /**
   * Critical frozen. Specimen must not be allowed to thaw until immediately prior to testing.
   */
  CriticalFrozen: v3_EntityHandling_CriticalFrozen,
  /**
   * Critical refrigerated - must not be allowed to freeze or warm until imediately prior to testing.
   */
  CriticalRefrigeratedTemperature: v3_EntityHandling_CriticalRefrigeratedTemperature,
  /**
   * Deep Frozen -16 to -20C.
   */
  DeepFrozen: v3_EntityHandling_DeepFrozen,
  /**
   * Keep in a dry environment
   */
  Dry: v3_EntityHandling_Dry,
  /**
   * Keep frozen below 0 ?C
   */
  Frozen: v3_EntityHandling_Frozen,
  /**
   * Container is free of heavy metals, including lead.
   */
  MetalFree: v3_EntityHandling_MetalFree,
  /**
   * Keep in liquid nitrogen
   */
  Nitrogen: v3_EntityHandling_Nitrogen,
  /**
   * Protect from light (eg. Wrap in aluminum foil).
   */
  ProtectFromLight: v3_EntityHandling_ProtectFromLight,
  /**
   * Do not shake
   */
  DoNotShake: v3_EntityHandling_DoNotShake,
  /**
   * Protect against shock
   */
  NoShock: v3_EntityHandling_NoShock,
  /**
   * Keep at refrigerated temperature:4-8C Accidental warming or freezing is of little consequence.
   */
  RefrigeratedTemperature: v3_EntityHandling_RefrigeratedTemperature,
  /**
   * Shake thoroughly before using
   */
  ShakeBeforeUse: v3_EntityHandling_ShakeBeforeUse,
  /**
   * Ultra cold frozen -75 to -85C.  Ultra cold freezer is typically at temperature of dry ice.
   */
  UltraFrozen: v3_EntityHandling_UltraFrozen,
  /**
   * Keep upright, do not turn upside down
   */
  Upright: v3_EntityHandling_Upright,
};
const v3_EntityNamePartQualifier_OrganizationNamePartQualifier: Coding = {
  code: '_OrganizationNamePartQualifier',
  display: 'OrganizationNamePartQualifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_PersonNamePartAffixTypes: Coding = {
  code: '_PersonNamePartAffixTypes',
  display: 'PersonNamePartAffixTypes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_PersonNamePartChangeQualifier: Coding = {
  code: '_PersonNamePartChangeQualifier',
  display: 'PersonNamePartChangeQualifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_PersonNamePartMiscQualifier: Coding = {
  code: '_PersonNamePartMiscQualifier',
  display: 'PersonNamePartMiscQualifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_PersonNamePartQualifier: Coding = {
  code: '_PersonNamePartQualifier',
  display: 'PersonNamePartQualifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_Academic: Coding = {
  code: 'AC',
  display: 'academic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_Adopted: Coding = {
  code: 'AD',
  display: 'adopted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_Birth: Coding = {
  code: 'BR',
  display: 'birth',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_Callme: Coding = {
  code: 'CL',
  display: 'callme',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_ContainerName: Coding = {
  code: 'CON',
  display: 'container name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_DeviceName: Coding = {
  code: 'DEV',
  display: 'device name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_FlavorName: Coding = {
  code: 'FLAV',
  display: 'FlavorName',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_FormulationPartName: Coding = {
  code: 'FORMUL',
  display: 'FormulationPartName',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_FormName: Coding = {
  code: 'FRM',
  display: 'form name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_Initial: Coding = {
  code: 'IN',
  display: 'initial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_InventedName: Coding = {
  code: 'INV',
  display: 'invented name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_LegalStatus: Coding = {
  code: 'LS',
  display: 'Legal status',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_Nobility: Coding = {
  code: 'NB',
  display: 'nobility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_PharmaceuticalEntityNamePartQualifiers: Coding = {
  code: 'PharmaceuticalEntityNamePartQualifiers',
  display: 'PharmaceuticalEntityNamePartQualifiers',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_TargetPopulationName: Coding = {
  code: 'POPUL',
  display: 'TargetPopulationName',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_Professional: Coding = {
  code: 'PR',
  display: 'professional',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_ScientificName: Coding = {
  code: 'SCI',
  display: 'scientific name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_Spouse: Coding = {
  code: 'SP',
  display: 'spouse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_StrengthName: Coding = {
  code: 'STR',
  display: 'strength name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_TimeOrPeriodName: Coding = {
  code: 'TIME',
  display: 'TimeOrPeriodName',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_Title: Coding = {
  code: 'TITLE',
  display: 'title',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_TrademarkName: Coding = {
  code: 'TMK',
  display: 'trademark name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
const v3_EntityNamePartQualifier_IntendedUseName: Coding = {
  code: 'USE',
  display: 'intended use name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifier',
};
/**
 *   OpenIssue:
 * Needs description
 */
export const V3EntityNamePartQualifier = {
  /**
   * OrganizationNamePartQualifier
   */
  OrganizationNamePartQualifier: v3_EntityNamePartQualifier_OrganizationNamePartQualifier,
  /**
   * PersonNamePartAffixTypes
   */
  PersonNamePartAffixTypes: v3_EntityNamePartQualifier_PersonNamePartAffixTypes,
  /**
   * PersonNamePartChangeQualifier
   */
  PersonNamePartChangeQualifier: v3_EntityNamePartQualifier_PersonNamePartChangeQualifier,
  /**
   * PersonNamePartMiscQualifier
   */
  PersonNamePartMiscQualifier: v3_EntityNamePartQualifier_PersonNamePartMiscQualifier,
  /**
   * PersonNamePartQualifier
   */
  PersonNamePartQualifier: v3_EntityNamePartQualifier_PersonNamePartQualifier,
  /**
   * Indicates that a prefix like "Dr." or a suffix like "M.D." or "Ph.D." is an academic title.
   */
  Academic: v3_EntityNamePartQualifier_Academic,
  /**
   * The name the person was given at the time of adoption.
   */
  Adopted: v3_EntityNamePartQualifier_Adopted,
  /**
   * A name that a person had shortly after being born. Usually for family names but may be used to mark given names at birth that may have changed later.
   */
  Birth: v3_EntityNamePartQualifier_Birth,
  /**
   * A callme name is (usually a given name) that is preferred when a person is directly addressed.
   */
  Callme: v3_EntityNamePartQualifier_Callme,
  /**
   * Description: This refers to the container if present in the medicinal product name.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               For Optaflu suspension for injection in pre-filled syringe Influenza vaccine (surface antigen, inactivated, prepared in cell culture) (2007/2008 season): pre-filled syringe
   */
  ContainerName: v3_EntityNamePartQualifier_ContainerName,
  /**
   * Description: This refers to the qualifiers in the name for devices and is at the moment mainly applicable to insulins and inhalation products.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               For the medicinal product Actrapid FlexPen 100 IU/ml Solution for injection Subcutaneous use: FlexPen.
   */
  DeviceName: v3_EntityNamePartQualifier_DeviceName,
  /**
   * Description: This refers to a flavor of the medicinal product if present in the medicinal product name.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            For 'CoughCure Linctus Orange Flavor', the flavor part is "Orange"
   *                            For 'Wonderdrug Syrup Cherry Flavor', the flavor part is "Cherry"
   */
  FlavorName: v3_EntityNamePartQualifier_FlavorName,
  /**
   * Description: This refers to the formulation of the medicinal product if present in the medicinal product name.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            For 'SpecialMed Sugar Free Cough Syrup', the formulation name part is "Sugar Free"
   *                            For 'QuickCure Gluten-free Bulk Fibre', the formulation name part is "gluten-free"
   */
  FormulationPartName: v3_EntityNamePartQualifier_FormulationPartName,
  /**
   * Description: This refers to the pharmaceutical form/ if present in the medicinal product name.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               For Agenerase 50 mg soft capsules: Soft Capsules
   *
   *
   *
   *                               For Ludiomil 25mg-Filmtabletten: Filmtabletten
   *
   *
   *
   *                               For Optaflu suspension for injection in pre-filled syringe Influenza vaccine (surface antigen, inactivated, prepared in cell culture) (2007/2008 season): suspension for injection
   */
  FormName: v3_EntityNamePartQualifier_FormName,
  /**
   * Indicates that a name part is just an initial. Initials do not imply a trailing period since this would not work with non-Latin scripts.  Initials may consist of more than one letter, e.g., "Ph." could stand for "Philippe" or "Th." for "Thomas".
   */
  Initial: v3_EntityNamePartQualifier_Initial,
  /**
   * Description: This refers to the product name without the trademark or the name of the marketing authorization holder or any other descriptor reflected in the product name and, if appropriate, whether it is intended e.g. for babies, children or adults.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               Agenerase
   *
   *
   *
   *                               Optaflu
   *
   *
   *
   *                               Ludiomil
   */
  InventedName: v3_EntityNamePartQualifier_InventedName,
  /**
   * For organizations a suffix indicating the legal status, e.g., "Inc.", "Co.", "AG", "GmbH", "B.V." "S.A.",  "Ltd." etc.
   */
  LegalStatus: v3_EntityNamePartQualifier_LegalStatus,
  /**
   * In Europe and Asia, there are still people with nobility titles (aristocrats).  German "von" is generally a nobility title, not a mere voorvoegsel.  Others are "Earl of" or "His Majesty King of..." etc.  Rarely used nowadays, but some systems do keep track of this.
   */
  Nobility: v3_EntityNamePartQualifier_Nobility,
  /**
   * Description: Medication Name Parts are a means of specifying a range of acceptable "official" forms of the name of a product.  They are used as patterns against which input name strings may be matched for automatic identification of products from input text reports.   While they cover the concepts held under "doseForm" or "route" or "strength" the name parts are not the same and do not fit into a controlled vocabulary in the same way. By specifying up to 8 name parts a much larger range of possible names can be generated.
   */
  PharmaceuticalEntityNamePartQualifiers: v3_EntityNamePartQualifier_PharmaceuticalEntityNamePartQualifiers,
  /**
   * Description: This refers to the target population for the medicinal product if present in the medicinal product name
   *
   *
   *                            Examples:
   *
   *
   *
   *                            For 'Broncho-Drug 3.5 mg-capsules for children', the target population part is "children"
   *                            For 'Adult Chesty Cough Syrup', the target population part is "adult"
   */
  TargetPopulationName: v3_EntityNamePartQualifier_TargetPopulationName,
  /**
   * Primarily in the British Imperial culture people tend to have an abbreviation of their professional organization as part of their credential suffices.
   */
  Professional: v3_EntityNamePartQualifier_Professional,
  /**
   * Description: This refers to the product common or scientific name without the trademark or the name of the marketing authorization holder or any other descriptor reflected in the product name.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               For Agenerase: N/A
   *
   *
   *
   *                               For Optaflu: Influenza vaccine (surface antigen, inactivated, prepared in cell culture) (2007/2008 season)
   *
   *
   *
   *                               For Ludiomil: N/A
   */
  ScientificName: v3_EntityNamePartQualifier_ScientificName,
  /**
   * The name assumed from the partner in a marital relationship (hence the "SP"). Usually the spouse's family name. Note that no inference about gender can be made from the existence of spouse names.
   */
  Spouse: v3_EntityNamePartQualifier_Spouse,
  /**
   * Description: This refers to the strength if present in the medicinal product name. The use of decimal points should be accommodated if required.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               For Agenerase 50 mg soft capsules: 50mg
   *
   *
   *
   *                               For Ludiomil 25mg-Filmtabletten: 25 mg
   *
   *
   *
   *                               For Optaflu suspension for injection in pre-filled syringe Influenza vaccine (surface antigen, inactivated, prepared in cell culture) (2007/2008 season): N/A
   */
  StrengthName: v3_EntityNamePartQualifier_StrengthName,
  /**
   * Description: This refers to a time or time period that may be specified in the text of the medicinal product name
   *
   *
   *                            Example:
   *
   *
   *
   *                            For an influenza vaccine 'Drug-FLU season 2008/2009', the time/period part is "2008/2009 season"
   */
  TimeOrPeriodName: v3_EntityNamePartQualifier_TimeOrPeriodName,
  /**
   * Indicates that a prefix or a suffix is a title that applies to the whole name, not just the adjacent name part.
   */
  Title: v3_EntityNamePartQualifier_Title,
  /**
   * Description: This refers to trademark/company element if present in the medicinal product name.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               for Insulin Human Winthrop Comb 15: Winthrop
   */
  TrademarkName: v3_EntityNamePartQualifier_TrademarkName,
  /**
   * Description: This refers to the intended use if present in the medicinal product name without the trademark or the name of the marketing authorization holder or any other descriptor reflected in the product name.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            For 'Drug-BI Caplets - Heartburn Relief', the intended use part is: "Heartburn Relief"
   *                            For 'Medicine Honey Syrup for Soothing Coughs' the intended use part is "Soothing Coughs"
   */
  IntendedUseName: v3_EntityNamePartQualifier_IntendedUseName,
  /**
   * A Dutch "voorvoegsel" is something like "van" or "de" that might have indicated nobility in the past but no longer so. Similar prefixes exist in other languages such as Spanish, French or Portugese.
   */
  Voorvoegsel: v3_EntityNamePartQualifier_Voorvoegsel,
};
const v3_EntityNamePartQualifierR2_ContainerName: Coding = {
  code: 'CON',
  display: 'container name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_DeviceName: Coding = {
  code: 'DEV',
  display: 'device name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_FlavorName: Coding = {
  code: 'FLAV',
  display: 'FlavorName',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_FormulationPartName: Coding = {
  code: 'FORMUL',
  display: 'FormulationPartName',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_FormName: Coding = {
  code: 'FRM',
  display: 'form name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_InventedName: Coding = {
  code: 'INV',
  display: 'invented name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_Prefix: Coding = {
  code: 'PFX',
  display: 'prefix',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_PharmaceuticalEntityNamePartQualifiers: Coding = {
  code: 'PharmaceuticalEntityNamePartQualifiers',
  display: 'PharmaceuticalEntityNamePartQualifiers',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_TargetPopulationName: Coding = {
  code: 'POPUL',
  display: 'TargetPopulationName',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_ScientificName: Coding = {
  code: 'SCI',
  display: 'scientific name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_Suffix: Coding = {
  code: 'SFX',
  display: 'suffix',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_StrengthName: Coding = {
  code: 'STR',
  display: 'strength name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_TimeOrPeriodName: Coding = {
  code: 'TIME',
  display: 'TimeOrPeriodName',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_TitleStyles: Coding = {
  code: 'TitleStyles',
  display: 'TitleStyles',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_TrademarkName: Coding = {
  code: 'TMK',
  display: 'trademark name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
const v3_EntityNamePartQualifierR2_IntendedUseName: Coding = {
  code: 'USE',
  display: 'intended use name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartQualifierR2',
};
/**
 *   Description:
 * The qualifier is a set of codes each of which specifies a certain subcategory of the name part in addition to the main name part type. For example, a given name may be flagged as a nickname, a family name may be a pseudonym or a name of public records.
 */
export const V3EntityNamePartQualifierR2 = {
  /**
   * Description:Indicates that a title like "Dr.", "M.D." or "Ph.D." is an academic title.
   */
  Academic: v3_EntityNamePartQualifierR2_Academic,
  /**
   * Description:A name part a person acquired.  The name part may be acquired by adoption, or the person may have chosen to use the name part for some other reason.
   *
   *
   *                            Note: this differs from an Other/Psuedonym/Alias in that an acquired name part is acquired on a formal basis rather than an informal one (e.g. registered as part of the official name).
   */
  Acquired: v3_EntityNamePartQualifierR2_Acquired,
  /**
   * Description:A name that a person was given at birth or established as a consequence of adoption.
   *
   *
   *                            Note: This is not used for temporary names assigned at birth such as "Baby of Smith" a" which is just a name with a use code of "TEMP".
   */
  Birth: v3_EntityNamePartQualifierR2_Birth,
  /**
   * Description:Used to indicate which of the various name parts is used when interacting with the person.
   */
  Callme: v3_EntityNamePartQualifierR2_Callme,
  /**
   * Description: This refers to the container if present in the medicinal product name.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               For Optaflu suspension for injection in pre-filled syringe Influenza vaccine (surface antigen, inactivated, prepared in cell culture) (2007/2008 season): pre-filled syringe
   */
  ContainerName: v3_EntityNamePartQualifierR2_ContainerName,
  /**
   * Description: This refers to the qualifiers in the name for devices and is at the moment mainly applicable to insulins and inhalation products.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               For the medicinal product Actrapid FlexPen 100 IU/ml Solution for injection Subcutaneous use: FlexPen.
   */
  DeviceName: v3_EntityNamePartQualifierR2_DeviceName,
  /**
   * Description: This refers to a flavor of the medicinal product if present in the medicinal product name.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            For 'CoughCure Linctus Orange Flavor', the flavor part is "Orange"
   *                            For 'Wonderdrug Syrup Cherry Flavor', the flavor part is "Cherry"
   */
  FlavorName: v3_EntityNamePartQualifierR2_FlavorName,
  /**
   * Description: This refers to the formulation of the medicinal product if present in the medicinal product name.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            For 'SpecialMed Sugar Free Cough Syrup', the formulation name part is "Sugar Free"
   *                            For 'QuickCure Gluten-free Bulk Fibre', the formulation name part is "gluten-free"
   */
  FormulationPartName: v3_EntityNamePartQualifierR2_FormulationPartName,
  /**
   * Description: This refers to the pharmaceutical form/ if present in the medicinal product name.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               For Agenerase 50 mg soft capsules: Soft Capsules
   *
   *
   *
   *                               For Ludiomil 25mg-Filmtabletten: Filmtabletten
   *
   *
   *
   *                               For Optaflu suspension for injection in pre-filled syringe Influenza vaccine (surface antigen, inactivated, prepared in cell culture) (2007/2008 season): suspension for injection
   */
  FormName: v3_EntityNamePartQualifierR2_FormName,
  /**
   * Description:A honorific such as "The Right Honourable" or "Weledelgeleerde Heer".
   */
  Honorific: v3_EntityNamePartQualifierR2_Honorific,
  /**
   * Description:Indicates that a name part is just an initial.  Initials do not imply a trailing period since this would not work with non-Latin scripts.  In some languages, initials may consist of more than one letter, e.g., "Ph" could stand for "Philippe" or "Th" For "Thomas".
   */
  Initial: v3_EntityNamePartQualifierR2_Initial,
  /**
   * Description: This refers to the product name without the trademark or the name of the marketing authorization holder or any other descriptor reflected in the product name and, if appropriate, whether it is intended e.g. for babies, children or adults.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               Agenerase
   *
   *
   *
   *                               Optaflu
   *
   *
   *
   *                               Ludiomil
   */
  InventedName: v3_EntityNamePartQualifierR2_InventedName,
  /**
   * Description:For organizations a suffix indicating the legal status, e.g., "Inc.", "Co.", "AG", "GmbH", "B.V." "S.A.", "Ltd." etc.
   */
  LegalStatus: v3_EntityNamePartQualifierR2_LegalStatus,
  /**
   * Description:Indicates that the name part is a middle name.
   *
   *
   *                            Usage Notes: In general, the english "middle name" concept is all of the given names after the first. This qualifier may be used to explicitly indicate which given names are considered to be middle names. The middle name qualifier may also be used with family names. This is a Scandinavian use case, matching the concept of "mellomnavn","mellannamn". Note that there are specific rules that indicate what names may be taken as a mellannamn in different Scandinavian countries.
   */
  MiddleName: v3_EntityNamePartQualifierR2_MiddleName,
  /**
   * Description:A nobility title such as Sir, Count, Grafin.
   */
  Nobility: v3_EntityNamePartQualifierR2_Nobility,
  /**
   * Description:A prefix has a strong association to the immediately following name part. A prefix has no implicit trailing white space (it has implicit leading white space though).
   */
  Prefix: v3_EntityNamePartQualifierR2_Prefix,
  /**
   * Description: Medication Name Parts are a means of specifying a range of acceptable "official" forms of the name of a product.  They are used as patterns against which input name strings may be matched for automatic identification of products from input text reports.   While they cover the concepts held under "doseForm" or "route" or "strength" the name parts are not the same and do not fit into a controlled vocabulary in the same way. By specifying up to 8 name parts a much larger range of possible names can be generated.
   */
  PharmaceuticalEntityNamePartQualifiers: v3_EntityNamePartQualifierR2_PharmaceuticalEntityNamePartQualifiers,
  /**
   * Description: This refers to the target population for the medicinal product if present in the medicinal product name
   *
   *
   *                            Examples:
   *
   *
   *
   *                            For 'Broncho-Drug 3.5 mg-capsules for children', the target population part is "children"
   *                            For 'Adult Chesty Cough Syrup', the target population part is "adult"
   */
  TargetPopulationName: v3_EntityNamePartQualifierR2_TargetPopulationName,
  /**
   * Description:Primarily in the British Imperial culture people tend to have an abbreviation of their professional organization as part of their credential titles.
   */
  Professional: v3_EntityNamePartQualifierR2_Professional,
  /**
   * Description: This refers to the product common or scientific name without the trademark or the name of the marketing authorization holder or any other descriptor reflected in the product name.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               For Agenerase: N/A
   *
   *
   *
   *                               For Optaflu: Influenza vaccine (surface antigen, inactivated, prepared in cell culture) (2007/2008 season)
   *
   *
   *
   *                               For Ludiomil: N/A
   */
  ScientificName: v3_EntityNamePartQualifierR2_ScientificName,
  /**
   * Description:A suffix has a strong association to the immediately preceding name part. A suffix has no implicit leading white space (it has implicit trailing white space though).
   */
  Suffix: v3_EntityNamePartQualifierR2_Suffix,
  /**
   * Description:The name assumed from the partner in a marital relationship.  Usually the spouse's family name.  Note that no inference about gender can be made from the existence of spouse names.
   */
  Spouse: v3_EntityNamePartQualifierR2_Spouse,
  /**
   * Description: This refers to the strength if present in the medicinal product name. The use of decimal points should be accommodated if required.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               For Agenerase 50 mg soft capsules: 50mg
   *
   *
   *
   *                               For Ludiomil 25mg-Filmtabletten: 25 mg
   *
   *
   *
   *                               For Optaflu suspension for injection in pre-filled syringe Influenza vaccine (surface antigen, inactivated, prepared in cell culture) (2007/2008 season): N/A
   */
  StrengthName: v3_EntityNamePartQualifierR2_StrengthName,
  /**
   * Description: This refers to a time or time period that may be specified in the text of the medicinal product name
   *
   *
   *                            Example:
   *
   *
   *
   *                            For an influenza vaccine 'Drug-FLU season 2008/2009', the time/period part is "2008/2009 season"
   */
  TimeOrPeriodName: v3_EntityNamePartQualifierR2_TimeOrPeriodName,
  /**
   * Description:Extra information about the style of a title
   */
  TitleStyles: v3_EntityNamePartQualifierR2_TitleStyles,
  /**
   * Description: This refers to trademark/company element if present in the medicinal product name.
   *
   *                         EXAMPLES:
   *
   *
   *
   *                               for Insulin Human Winthrop Comb 15: Winthrop
   */
  TrademarkName: v3_EntityNamePartQualifierR2_TrademarkName,
  /**
   * Description: This refers to the intended use if present in the medicinal product name without the trademark or the name of the marketing authorization holder or any other descriptor reflected in the product name.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            For 'Drug-BI Caplets - Heartburn Relief', the intended use part is: "Heartburn Relief"
   *                            For 'Medicine Honey Syrup for Soothing Coughs' the intended use part is "Soothing Coughs"
   */
  IntendedUseName: v3_EntityNamePartQualifierR2_IntendedUseName,
};
const v3_EntityNamePartType_Delimiter: Coding = {
  code: 'DEL',
  display: 'delimiter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartType',
};
const v3_EntityNamePartType_Family: Coding = {
  code: 'FAM',
  display: 'family',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartType',
};
const v3_EntityNamePartType_Given: Coding = {
  code: 'GIV',
  display: 'given',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartType',
};
const v3_EntityNamePartType_Prefix: Coding = {
  code: 'PFX',
  display: 'prefix',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartType',
};
const v3_EntityNamePartType_Suffix: Coding = {
  code: 'SFX',
  display: 'suffix',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartType',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3EntityNamePartType = {
  /**
   * A delimiter has no meaning other than being literally printed in this name representation.  A delimiter has no implicit leading and trailing white space.
   */
  Delimiter: v3_EntityNamePartType_Delimiter,
  /**
   * Family name, this is the name that links to the genealogy. In some cultures (e.g. Eritrea) the family name of a son is the first name of his father.
   */
  Family: v3_EntityNamePartType_Family,
  /**
   * Given name (don't call it "first name" since this given names do not always come first)
   */
  Given: v3_EntityNamePartType_Given,
  /**
   * A prefix has a strong association to the immediately following name part. A prefix has no implicit trailing white space (it has implicit leading white space though). Note that prefixes can be inverted.
   */
  Prefix: v3_EntityNamePartType_Prefix,
  /**
   * Description:A suffix has a strong association to the immediately preceding name part. A suffix has no implicit leading white space (it has implicit trailing white space though). Suffices cannot be inverted.
   */
  Suffix: v3_EntityNamePartType_Suffix,
};
const v3_EntityNamePartTypeR2_Delimiter: Coding = {
  code: 'DEL',
  display: 'delimiter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartTypeR2',
};
const v3_EntityNamePartTypeR2_Family: Coding = {
  code: 'FAM',
  display: 'family',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartTypeR2',
};
const v3_EntityNamePartTypeR2_Given: Coding = {
  code: 'GIV',
  display: 'given',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartTypeR2',
};
const v3_EntityNamePartTypeR2_Title: Coding = {
  code: 'TITLE',
  display: 'title',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNamePartTypeR2',
};
/**
 *   Description:
 * Indicates whether the name part is a given name, family name, prefix, suffix, etc.
 */
export const V3EntityNamePartTypeR2 = {
  /**
   * Description:A delimiter has no meaning other than being literally printed in this name representation. A delimiter has no implicit leading and trailing white space.
   */
  Delimiter: v3_EntityNamePartTypeR2_Delimiter,
  /**
   * Description:Family name, this is the name that links to the genealogy. In some cultures (e.g. Eritrea) the family name of a son is the first name of his father.
   */
  Family: v3_EntityNamePartTypeR2_Family,
  /**
   * Description:Given name. Note: don't call it "first name" since the given names do not always come first.
   */
  Given: v3_EntityNamePartTypeR2_Given,
  /**
   * Description:Part of the name that is acquired as a title due to academic, legal, employment or nobility status etc.
   *
   *
   *                            Note:Title name parts include name parts that come after the name such as qualifications, even if they are not always considered to be titles.
   */
  Title: v3_EntityNamePartTypeR2_Title,
};
const v3_EntityNameUse_NameRepresentationUse: Coding = {
  code: '_NameRepresentationUse',
  display: 'NameRepresentationUse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
const v3_EntityNameUse_ArtistStage: Coding = {
  code: 'A',
  display: 'Artist/Stage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
const v3_EntityNameUse_Assigned: Coding = {
  code: 'ASGN',
  display: 'assigned',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
const v3_EntityNameUse_License: Coding = {
  code: 'C',
  display: 'License',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
const v3_EntityNameUse_IndigenousTribal: Coding = {
  code: 'I',
  display: 'Indigenous/Tribal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
const v3_EntityNameUse_Legal: Coding = {
  code: 'L',
  display: 'Legal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
const v3_EntityNameUse_OfficialRegistry: Coding = {
  code: 'OR',
  display: 'official registry',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
const v3_EntityNameUse_Pseudonym: Coding = {
  code: 'P',
  display: 'pseudonym',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
const v3_EntityNameUse_Phonetic: Coding = {
  code: 'PHON',
  display: 'phonetic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
const v3_EntityNameUse_Religious: Coding = {
  code: 'R',
  display: 'Religious',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
const v3_EntityNameUse_Soundex: Coding = {
  code: 'SNDX',
  display: 'Soundex',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
const v3_EntityNameUse_Search: Coding = {
  code: 'SRCH',
  display: 'search',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUse',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3EntityNameUse = {
  /**
   * Identifies the different representations of a name.  The representation may affect how the name is used.  (E.g. use of Ideographic for formal communications.)
   */
  NameRepresentationUse: v3_EntityNameUse_NameRepresentationUse,
  /**
   * Includes writer's pseudonym, stage name, etc
   */
  ArtistStage: v3_EntityNameUse_ArtistStage,
  /**
   * Alphabetic transcription of name (Japanese: romaji)
   */
  Alphabetic: v3_EntityNameUse_Alphabetic,
  /**
   * A name assigned to a person. Reasons some organizations assign alternate names may include not knowing the person's name, or to maintain anonymity. Some, but not necessarily all, of the name types that people call "alias" may fit into this category.
   */
  Assigned: v3_EntityNameUse_Assigned,
  /**
   * As recorded on a license, record, certificate, etc. (only if different from legal name)
   */
  License: v3_EntityNameUse_License,
  /**
   * e.g. Chief Red Cloud
   */
  IndigenousTribal: v3_EntityNameUse_IndigenousTribal,
  /**
   * Ideographic representation of name (e.g., Japanese kanji, Chinese characters)
   */
  Ideographic: v3_EntityNameUse_Ideographic,
  /**
   * Known as/conventional/the one you use
   */
  Legal: v3_EntityNameUse_Legal,
  /**
   * Definition:The formal name as registered in an official (government) registry, but which name might not be commonly used. Particularly used in countries with a law system based on Napoleonic law.
   */
  OfficialRegistry: v3_EntityNameUse_OfficialRegistry,
  /**
   * A self asserted name that the person is using or has used.
   */
  Pseudonym: v3_EntityNameUse_Pseudonym,
  /**
   * A name spelled phonetically.
   *
   *                         There are a variety of phonetic spelling algorithms. This code value does not distinguish between these.Discussion:
   */
  Phonetic: v3_EntityNameUse_Phonetic,
  /**
   * e.g. Sister Mary Francis, Brother John
   */
  Religious: v3_EntityNameUse_Religious,
  /**
   * A name spelled according to the SoundEx algorithm.
   */
  Soundex: v3_EntityNameUse_Soundex,
  /**
   * A name intended for use in searching or matching.
   */
  Search: v3_EntityNameUse_Search,
  /**
   * Syllabic transcription of name (e.g., Japanese kana, Korean hangul)
   */
  Syllabic: v3_EntityNameUse_Syllabic,
};
const v3_EntityNameUseR2_BusinessName: Coding = {
  code: 'A',
  display: 'business name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_Alphabetic: Coding = {
  code: 'ABC',
  display: 'alphabetic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_Anonymous: Coding = {
  code: 'ANON',
  display: 'Anonymous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_Assumed: Coding = {
  code: 'Assumed',
  display: 'Assumed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_Customary: Coding = {
  code: 'C',
  display: 'customary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_DoNotUse: Coding = {
  code: 'DN',
  display: 'do not use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_IndigenousTribal: Coding = {
  code: 'I',
  display: 'Indigenous/Tribal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_Ideographic: Coding = {
  code: 'IDE',
  display: 'ideographic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_MaidenName: Coding = {
  code: 'M',
  display: 'maiden name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_NameRepresentationUse: Coding = {
  code: 'NameRepresentationUse',
  display: 'NameRepresentationUse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_NoLongerInUse: Coding = {
  code: 'OLD',
  display: 'no longer in use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_OfficialRegistryName: Coding = {
  code: 'OR',
  display: 'official registry name',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_OtherPseudonymAlias: Coding = {
  code: 'P',
  display: 'Other/Pseudonym/Alias',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_Phonetic: Coding = {
  code: 'PHON',
  display: 'phonetic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_Religious: Coding = {
  code: 'R',
  display: 'religious',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_Search: Coding = {
  code: 'SRCH',
  display: 'search',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_Syllabic: Coding = {
  code: 'SYL',
  display: 'syllabic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
const v3_EntityNameUseR2_Temporary: Coding = {
  code: 'T',
  display: 'temporary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityNameUseR2',
};
/**
 *   Description:
 * A set of codes advising a system or user which name in a set of names to select for a given purpose.
 */
export const V3EntityNameUseR2 = {
  /**
   * Description:A name used in a Professional or Business context .  Examples: Continuing to use a maiden name in a professional context, or using a stage performing name (some of these names are also pseudonyms)
   */
  BusinessName: v3_EntityNameUseR2_BusinessName,
  /**
   * Description:Alphabetic transcription of name in latin alphabet (Japanese: romaji)
   */
  Alphabetic: v3_EntityNameUseR2_Alphabetic,
  /**
   * Description:Anonymous assigned name (used to protect a persons identity for privacy reasons)
   */
  Anonymous: v3_EntityNameUseR2_Anonymous,
  /**
   * Description:A name that a person has assumed or has been assumed to them.
   */
  Assumed: v3_EntityNameUseR2_Assumed,
  /**
   * Description:Known as/conventional/the one you normally use
   */
  Customary: v3_EntityNameUseR2_Customary,
  /**
   * Description:This name should no longer be used when interacting with the person (i.e .  in addition to no longer being used, the name should not be even mentioned when interacting with the person)Note: applications are not required to compare names labeled "Do Not Use" and other names in order to eliminate name parts that are common between the other name and a name labeled "Do Not Use".
   */
  DoNotUse: v3_EntityNameUseR2_DoNotUse,
  /**
   * Description:e.g .  Chief Red Cloud
   */
  IndigenousTribal: v3_EntityNameUseR2_IndigenousTribal,
  /**
   * Description:Ideographic representation of name (e.g., Japanese kanji, Chinese characters)
   */
  Ideographic: v3_EntityNameUseR2_Ideographic,
  /**
   * Description:A name used prior to marriage.Note that marriage naming customs vary greatly around the world.  This name use is for use by applications that collect and store maiden names.  Though the concept of maiden name is often gender specific, the use of this term is not gender specific.  The use of this term does not imply any particular history for a person's name, nor should the maiden name be determined algorithmically
   */
  MaidenName: v3_EntityNameUseR2_MaidenName,
  /**
   * Description:Identifies the different representations of a name .  The representation may affect how the name is used .  (E.g .  use of Ideographic for formal communications.)
   */
  NameRepresentationUse: v3_EntityNameUseR2_NameRepresentationUse,
  /**
   * Description:This name is no longer in use (note: Names may also carry valid time ranges .  This code is used to cover the situations where it is known that the name is no longer valid, but no particular time range for its use is known)
   */
  NoLongerInUse: v3_EntityNameUseR2_NoLongerInUse,
  /**
   * Description:The formal name as registered in an official (government) registry, but which name might not be commonly used .  May correspond to the concept of legal name
   */
  OfficialRegistryName: v3_EntityNameUseR2_OfficialRegistryName,
  /**
   * Description:A non-official name by which the person is sometimes known.  (This may also be used to record informal names such as a nickname)
   */
  OtherPseudonymAlias: v3_EntityNameUseR2_OtherPseudonymAlias,
  /**
   * Description:The name as understood by the data enterer, i.e. a close approximation of a phonetic spelling of the name, not based on a phonetic algorithm.
   */
  Phonetic: v3_EntityNameUseR2_Phonetic,
  /**
   * Description:A name assumed as part of a religious vocation .  e.g .  Sister Mary Francis, Brother John
   */
  Religious: v3_EntityNameUseR2_Religious,
  /**
   * Description:A name intended for use in searching or matching.  This is used when the name is incomplete and contains enough details for search matching, but not enough for other uses.
   */
  Search: v3_EntityNameUseR2_Search,
  /**
   * Description:Syllabic transcription of name (e.g., Japanese kana, Korean hangul)
   */
  Syllabic: v3_EntityNameUseR2_Syllabic,
  /**
   * Description:A temporary name.  Note that a name valid time can provide more detailed information.  This may also be used for temporary names assigned at birth or in emergency situations.
   */
  Temporary: v3_EntityNameUseR2_Temporary,
};
const v3_EntityRisk_Aggressive: Coding = {
  code: 'AGG',
  display: 'aggressive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityRisk',
};
const v3_EntityRisk_Biohazard: Coding = {
  code: 'BHZ',
  display: 'biohazard',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityRisk',
};
const v3_EntityRisk_Biological: Coding = {
  code: 'BIO',
  display: 'Biological',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityRisk',
};
const v3_EntityRisk_Corrosive: Coding = {
  code: 'COR',
  display: 'Corrosive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityRisk',
};
const v3_EntityRisk_EscapeRisk: Coding = {
  code: 'ESC',
  display: 'Escape Risk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityRisk',
};
const v3_EntityRisk_Explosive: Coding = {
  code: 'EXP',
  display: 'explosive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityRisk',
};
const v3_EntityRisk_Inflammable: Coding = {
  code: 'IFL',
  display: 'inflammable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityRisk',
};
const v3_EntityRisk_Infectious: Coding = {
  code: 'INF',
  display: 'infectious',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityRisk',
};
const v3_EntityRisk_InjuryHazard: Coding = {
  code: 'INJ',
  display: 'injury hazard',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityRisk',
};
const v3_EntityRisk_Poison: Coding = {
  code: 'POI',
  display: 'poison',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityRisk',
};
const v3_EntityRisk_Radioactive: Coding = {
  code: 'RAD',
  display: 'radioactive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityRisk',
};
/**
 *  Kinds of risks associated with the handling of the material..
 */
export const V3EntityRisk = {
  /**
   * A danger that can be associated with certain living subjects, including humans.
   */
  Aggressive: v3_EntityRisk_Aggressive,
  /**
   * Material contains microorganisms that is an environmental hazard.  Must be handled with special care.
   */
  Biohazard: v3_EntityRisk_Biohazard,
  /**
   * The dangers associated with normal biological materials. I.e. potential risk of unknown infections.  Routine biological materials from living subjects.
   */
  Biological: v3_EntityRisk_Biological,
  /**
   * Material is corrosive and may cause severe injury to skin, mucous membranes and eyes. Avoid any unprotected contact.
   */
  Corrosive: v3_EntityRisk_Corrosive,
  /**
   * The entity is at risk for escaping from containment or control.
   */
  EscapeRisk: v3_EntityRisk_EscapeRisk,
  /**
   * Material is an explosive mixture.  Keep away from fire, sparks, and heat.
   */
  Explosive: v3_EntityRisk_Explosive,
  /**
   * Material is highly inflammable and in certain mixtures (with air) may lead to explosions.  Keep away from fire, sparks and excessive heat.
   */
  Inflammable: v3_EntityRisk_Inflammable,
  /**
   * Material known to be infectious with human pathogenic microorganisms.  Those who handle this material must take precautions for their protection.
   */
  Infectious: v3_EntityRisk_Infectious,
  /**
   * Material is solid and sharp (e.g., cannulas).  Dispose in hard container.
   */
  InjuryHazard: v3_EntityRisk_InjuryHazard,
  /**
   * Material is poisonous to humans and/or animals.  Special care must be taken to avoid incorporation, even of small amounts.
   */
  Poison: v3_EntityRisk_Poison,
  /**
   * Material is a source for ionizing radiation and must be handled with special care to avoid injury of those who handle it and to avoid environmental hazards.
   */
  Radioactive: v3_EntityRisk_Radioactive,
};
const v3_EntityStatus_Active: Coding = {
  code: 'active',
  display: 'active',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityStatus',
};
const v3_EntityStatus_Inactive: Coding = {
  code: 'inactive',
  display: 'inactive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityStatus',
};
const v3_EntityStatus_Normal: Coding = {
  code: 'normal',
  display: 'normal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityStatus',
};
const v3_EntityStatus_Nullified: Coding = {
  code: 'nullified',
  display: 'nullified',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityStatus',
};
const v3_EntityStatus_Terminated: Coding = {
  code: 'terminated',
  display: 'terminated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EntityStatus',
};
/**
 *  Codes representing the defined possible states of an Entity, as defined by the Entity class state machine.
 */
export const V3EntityStatus = {
  /**
   * The state representing the fact that the Entity record is currently active.
   */
  Active: v3_EntityStatus_Active,
  /**
   * Definition: The state representing the fact that the entity is inactive.
   */
  Inactive: v3_EntityStatus_Inactive,
  /**
   * The 'typical' state. Excludes "nullified" which represents the termination state of an Entity record instance that was created in error.
   */
  Normal: v3_EntityStatus_Normal,
  /**
   * The state representing the termination of an Entity record instance that was created in error.
   */
  Nullified: v3_EntityStatus_Nullified,
  /**
   * The state representing the normal termination of an Entity record.
   */
  Terminated: v3_EntityStatus_Terminated,
};
const v3_EquipmentAlertLevel_Critical: Coding = {
  code: 'C',
  display: 'Critical',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EquipmentAlertLevel',
};
const v3_EquipmentAlertLevel_Normal: Coding = {
  code: 'N',
  display: 'Normal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EquipmentAlertLevel',
};
const v3_EquipmentAlertLevel_Serious: Coding = {
  code: 'S',
  display: 'Serious',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EquipmentAlertLevel',
};
const v3_EquipmentAlertLevel_Warning: Coding = {
  code: 'W',
  display: 'Warning',
  system: 'http://terminology.hl7.org/CodeSystem/v3-EquipmentAlertLevel',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3EquipmentAlertLevel = {
  /**
   * Shut Down, Fix Problem and Re-init
   */
  Critical: v3_EquipmentAlertLevel_Critical,
  /**
   * No Corrective Action Needed
   */
  Normal: v3_EquipmentAlertLevel_Normal,
  /**
   * Corrective Action Required
   */
  Serious: v3_EquipmentAlertLevel_Serious,
  /**
   * Corrective Action Anticipated
   */
  Warning: v3_EquipmentAlertLevel_Warning,
};
const v3_Ethnicity_HispanicOrLatino: Coding = {
  code: '2135-2',
  display: 'Hispanic or Latino',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Spaniard: Coding = {
  code: '2137-8',
  display: 'Spaniard',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Andalusian: Coding = {
  code: '2138-6',
  display: 'Andalusian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Asturian: Coding = {
  code: '2139-4',
  display: 'Asturian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Castillian: Coding = {
  code: '2140-2',
  display: 'Castillian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Catalonian: Coding = {
  code: '2141-0',
  display: 'Catalonian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_BelearicIslander: Coding = {
  code: '2142-8',
  display: 'Belearic Islander',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Gallego: Coding = {
  code: '2143-6',
  display: 'Gallego',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Valencian: Coding = {
  code: '2144-4',
  display: 'Valencian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Canarian: Coding = {
  code: '2145-1',
  display: 'Canarian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_SpanishBasque: Coding = {
  code: '2146-9',
  display: 'Spanish Basque',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Mexican: Coding = {
  code: '2148-5',
  display: 'Mexican',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_MexicanAmerican: Coding = {
  code: '2149-3',
  display: 'Mexican American',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Mexicano: Coding = {
  code: '2150-1',
  display: 'Mexicano',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Chicano: Coding = {
  code: '2151-9',
  display: 'Chicano',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_LaRaza: Coding = {
  code: '2152-7',
  display: 'La Raza',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_MexicanAmericanIndian: Coding = {
  code: '2153-5',
  display: 'Mexican American Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_CentralAmerican: Coding = {
  code: '2155-0',
  display: 'Central American',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_CostaRican: Coding = {
  code: '2156-8',
  display: 'Costa Rican',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Guatemalan: Coding = {
  code: '2157-6',
  display: 'Guatemalan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Honduran: Coding = {
  code: '2158-4',
  display: 'Honduran',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Nicaraguan: Coding = {
  code: '2159-2',
  display: 'Nicaraguan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Panamanian: Coding = {
  code: '2160-0',
  display: 'Panamanian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Salvadoran: Coding = {
  code: '2161-8',
  display: 'Salvadoran',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_CentralAmericanIndian: Coding = {
  code: '2162-6',
  display: 'Central American Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_CanalZone: Coding = {
  code: '2163-4',
  display: 'Canal Zone',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_SouthAmerican: Coding = {
  code: '2165-9',
  display: 'South American',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Argentinean: Coding = {
  code: '2166-7',
  display: 'Argentinean',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Bolivian: Coding = {
  code: '2167-5',
  display: 'Bolivian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Chilean: Coding = {
  code: '2168-3',
  display: 'Chilean',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Colombian: Coding = {
  code: '2169-1',
  display: 'Colombian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Ecuadorian: Coding = {
  code: '2170-9',
  display: 'Ecuadorian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Paraguayan: Coding = {
  code: '2171-7',
  display: 'Paraguayan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Peruvian: Coding = {
  code: '2172-5',
  display: 'Peruvian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Uruguayan: Coding = {
  code: '2173-3',
  display: 'Uruguayan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Venezuelan: Coding = {
  code: '2174-1',
  display: 'Venezuelan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_SouthAmericanIndian: Coding = {
  code: '2175-8',
  display: 'South American Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Criollo: Coding = {
  code: '2176-6',
  display: 'Criollo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_LatinAmerican: Coding = {
  code: '2178-2',
  display: 'Latin American',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_PuertoRican: Coding = {
  code: '2180-8',
  display: 'Puerto Rican',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Cuban: Coding = {
  code: '2182-4',
  display: 'Cuban',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_Dominican: Coding = {
  code: '2184-0',
  display: 'Dominican',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
const v3_Ethnicity_NotHispanicOrLatino: Coding = {
  code: '2186-5',
  display: 'Not Hispanic or Latino',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Ethnicity',
};
/**
 *  In the United States, federal standards for classifying data on ethnicity determine the categories used by federal agencies and exert a strong influence on categorization by state and local agencies and private sector organizations. The federal standards do not conceptually define ethnicity, and they recognize the absence of an anthropological or scientific basis for ethnicity classification.  Instead, the federal standards acknowledge that ethnicity is a social-political construct in which an individual's own identification with a particular ethnicity is preferred to observer identification.  The standards specify two minimum ethnicity categories: Hispanic or Latino, and Not Hispanic or Latino.  The standards define a Hispanic or Latino as a person of "Mexican, Puerto Rican, Cuban, South or Central America, or other Spanish culture or origin, regardless of race." The standards stipulate that ethnicity data need not be limited to the two minimum categories, but any expansion must be collapsible to those categories.  In addition, the standards stipulate that an individual can be Hispanic or Latino or can be Not Hispanic or Latino, but cannot be both.
 */
export const V3Ethnicity = {
  /**
   * Hispanic or Latino
   */
  HispanicOrLatino: v3_Ethnicity_HispanicOrLatino,
  /**
   * Spaniard
   */
  Spaniard: v3_Ethnicity_Spaniard,
  /**
   * Andalusian
   */
  Andalusian: v3_Ethnicity_Andalusian,
  /**
   * Asturian
   */
  Asturian: v3_Ethnicity_Asturian,
  /**
   * Castillian
   */
  Castillian: v3_Ethnicity_Castillian,
  /**
   * Catalonian
   */
  Catalonian: v3_Ethnicity_Catalonian,
  /**
   * Belearic Islander
   */
  BelearicIslander: v3_Ethnicity_BelearicIslander,
  /**
   * Gallego
   */
  Gallego: v3_Ethnicity_Gallego,
  /**
   * Valencian
   */
  Valencian: v3_Ethnicity_Valencian,
  /**
   * Canarian
   */
  Canarian: v3_Ethnicity_Canarian,
  /**
   * Spanish Basque
   */
  SpanishBasque: v3_Ethnicity_SpanishBasque,
  /**
   * Mexican
   */
  Mexican: v3_Ethnicity_Mexican,
  /**
   * Mexican American
   */
  MexicanAmerican: v3_Ethnicity_MexicanAmerican,
  /**
   * Mexicano
   */
  Mexicano: v3_Ethnicity_Mexicano,
  /**
   * Chicano
   */
  Chicano: v3_Ethnicity_Chicano,
  /**
   * La Raza
   */
  LaRaza: v3_Ethnicity_LaRaza,
  /**
   * Mexican American Indian
   */
  MexicanAmericanIndian: v3_Ethnicity_MexicanAmericanIndian,
  /**
   * Central American
   */
  CentralAmerican: v3_Ethnicity_CentralAmerican,
  /**
   * Costa Rican
   */
  CostaRican: v3_Ethnicity_CostaRican,
  /**
   * Guatemalan
   */
  Guatemalan: v3_Ethnicity_Guatemalan,
  /**
   * Honduran
   */
  Honduran: v3_Ethnicity_Honduran,
  /**
   * Nicaraguan
   */
  Nicaraguan: v3_Ethnicity_Nicaraguan,
  /**
   * Panamanian
   */
  Panamanian: v3_Ethnicity_Panamanian,
  /**
   * Salvadoran
   */
  Salvadoran: v3_Ethnicity_Salvadoran,
  /**
   * Central American Indian
   */
  CentralAmericanIndian: v3_Ethnicity_CentralAmericanIndian,
  /**
   * Canal Zone
   */
  CanalZone: v3_Ethnicity_CanalZone,
  /**
   * South American
   */
  SouthAmerican: v3_Ethnicity_SouthAmerican,
  /**
   * Argentinean
   */
  Argentinean: v3_Ethnicity_Argentinean,
  /**
   * Bolivian
   */
  Bolivian: v3_Ethnicity_Bolivian,
  /**
   * Chilean
   */
  Chilean: v3_Ethnicity_Chilean,
  /**
   * Colombian
   */
  Colombian: v3_Ethnicity_Colombian,
  /**
   * Ecuadorian
   */
  Ecuadorian: v3_Ethnicity_Ecuadorian,
  /**
   * Paraguayan
   */
  Paraguayan: v3_Ethnicity_Paraguayan,
  /**
   * Peruvian
   */
  Peruvian: v3_Ethnicity_Peruvian,
  /**
   * Uruguayan
   */
  Uruguayan: v3_Ethnicity_Uruguayan,
  /**
   * Venezuelan
   */
  Venezuelan: v3_Ethnicity_Venezuelan,
  /**
   * South American Indian
   */
  SouthAmericanIndian: v3_Ethnicity_SouthAmericanIndian,
  /**
   * Criollo
   */
  Criollo: v3_Ethnicity_Criollo,
  /**
   * Latin American
   */
  LatinAmerican: v3_Ethnicity_LatinAmerican,
  /**
   * Puerto Rican
   */
  PuertoRican: v3_Ethnicity_PuertoRican,
  /**
   * Cuban
   */
  Cuban: v3_Ethnicity_Cuban,
  /**
   * Dominican
   */
  Dominican: v3_Ethnicity_Dominican,
  /**
   * Note that this term remains in the table for completeness, even though within HL7, the notion of "not otherwise coded" term is deprecated.
   */
  NotHispanicOrLatino: v3_Ethnicity_NotHispanicOrLatino,
};
const v3_ExposureMode_ExposureMode: Coding = {
  code: '_ExposureMode',
  display: 'ExposureMode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ExposureMode',
};
const v3_ExposureMode_Airborne: Coding = {
  code: 'AIRBORNE',
  display: 'airborne',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ExposureMode',
};
const v3_ExposureMode_Contact: Coding = {
  code: 'CONTACT',
  display: 'contact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ExposureMode',
};
const v3_ExposureMode_Foodborne: Coding = {
  code: 'FOODBORNE',
  display: 'foodborne',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ExposureMode',
};
const v3_ExposureMode_Waterborne: Coding = {
  code: 'WATERBORNE',
  display: 'waterborne',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ExposureMode',
};
/**
 *  Code for the mechanism by which the exposure agent was exchanged or potentially exchanged by the participants involved in the exposure.
 */
export const V3ExposureMode = {
  /**
   * Code for the mechanism by which the exposure agent was exchanged or potentially exchanged by the participants involved in the exposure.
   */
  ExposureMode: v3_ExposureMode_ExposureMode,
  /**
   * Description: Communication of an agent from a living subject or environmental source to a living subject through indirect contact via oral or nasal inhalation.
   */
  Airborne: v3_ExposureMode_Airborne,
  /**
   * Description: Communication of an agent from a living subject or environmental source to a living subject through direct physical contact.
   */
  Contact: v3_ExposureMode_Contact,
  /**
   * Description: Communication of an agent from a food source to a living subject via oral consumption.
   */
  Foodborne: v3_ExposureMode_Foodborne,
  /**
   * Description: Communication of an agent to a living subject by contact and/or consumption via an aqueous medium
   */
  Waterborne: v3_ExposureMode_Waterborne,
};
/**
 *  A relationship between two people characterizing their "familial" relationship
 */
export const V3FamilyMember = {
  /**
   * The player of the role (father) is a male who has taken the scoper (child) into their family through legal means and raises them as his own child.
   */
  AdoptiveFather: v3_RoleCode_AdoptiveFather,
  /**
   * The player of the role (father) is a female who has taken the scoper (child) into their family through legal means and raises them as her own child.
   */
  AdoptiveMother: v3_RoleCode_AdoptiveMother,
  /**
   * The player of the role (parent) has taken the scoper (child) into their family through legal means and raises them as his or her own child.
   */
  AdoptiveParent: v3_RoleCode_AdoptiveParent,
  /**
   * The player of the role is a sister of the scoping person's mother or father.
   */
  Aunt: v3_RoleCode_Aunt,
  /**
   * The player of the role is a male sharing one or both parents in common with the scoping entity.
   */
  Brother: v3_RoleCode_Brother,
  /**
   * The player of the role is: (1) a brother of the scoping person's spouse, or (2) the husband of the scoping person's sister, or (3) the husband of a sister of the scoping person's spouse.
   */
  BrotherInLaw: v3_RoleCode_BrotherInLaw,
  /**
   * The player of the role is a child of the scoping entity.
   */
  Child: v3_RoleCode_Child,
  /**
   * The player of the role is a child taken into a family through legal means and raised by the scoping person (parent) as his or her own child.
   */
  AdoptedChild: v3_RoleCode_AdoptedChild,
  /**
   * The player of the role is a child receiving parental care and nurture from the scoping person (parent) but not related to him or her through legal or blood ties.
   */
  FosterChild: v3_RoleCode_FosterChild,
  /**
   * The player of the role is the spouse of scoping person's child.
   */
  ChildInLaw: v3_RoleCode_ChildInLaw,
  /**
   * The player of the role is a relative of the scoping person descended from a common ancestor, such as a 	grandparent, by two or more steps in a diverging line.
   */
  Cousin: v3_RoleCode_Cousin,
  /**
   * The player of the role is a female offspring of the scoping entity (parent).
   */
  NaturalDaughter: v3_RoleCode_NaturalDaughter,
  /**
   * The player of the role is a female child taken into a family through legal means and raised by the scoping person (parent) as his or her own child.
   */
  AdoptedDaughter: v3_RoleCode_AdoptedDaughter,
  /**
   * Description: The player of the role is a female child (of any type) of scoping entity (parent)
   */
  Daughter: v3_RoleCode_Daughter,
  /**
   * The player of the role is a female child receiving parental care and nurture from the scoping person (parent) but not related to him or her through legal or blood ties.
   */
  FosterDaughter: v3_RoleCode_FosterDaughter,
  /**
   * The player of the role is the wife of scoping person's son.
   */
  DaughterInLaw: v3_RoleCode_DaughterInLaw,
  /**
   * The player of the role cohabits with the scoping person but is not the scoping person's spouse.
   */
  DomesticPartner: v3_RoleCode_DomesticPartner,
  /**
   * Description: A family member not having an immediate genetic or legal relationship e.g. Aunt, cousin, great grandparent, grandchild, grandparent, niece, nephew or uncle.
   */
  ExtendedFamilyMember: v3_RoleCode_ExtendedFamilyMember,
  /**
   * A relationship between two people characterizing their "familial" relationship
   */
  FamilyMember: v3_RoleCode_FamilyMember,
  /**
   * Player of the role was previously joined to the scoping person in marriage and this marriage is now dissolved and inactive.
   *
   *
   *                            Usage Note: This is significant to indicate as some jurisdictions have different legal requirements for former spouse to access the patient's record, from a general friend.
   */
  FormerSpouse: v3_RoleCode_FormerSpouse,
  /**
   * The player of the role is a male who begets or raises or nurtures the scoping entity (child).
   */
  Father: v3_RoleCode_Father,
  /**
   * The player of the role (parent) who is a male state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterFather: v3_RoleCode_FosterFather,
  /**
   * The player of the role is the father of the scoping person's husband or wife.
   */
  FatherInLaw: v3_RoleCode_FatherInLaw,
  /**
   * The scoper and player were carried in the same womb and share common biological parents but are the product of distinct egg/sperm pairs.
   */
  FraternalTwin: v3_RoleCode_FraternalTwin,
  /**
   * The scoper was carried in the same womb as the male player and shares common biological parents but is the product of a distinct egg/sperm pair.
   */
  FraternalTwinBrother: v3_RoleCode_FraternalTwinBrother,
  /**
   * The scoper was carried in the same womb as the female player and shares common biological parents but is the product of a distinct egg/sperm pair.
   */
  FraternalTwinSister: v3_RoleCode_FraternalTwinSister,
  /**
   * The player is a female whose womb carries the fetus of the scoper.  Generally used when the gestational mother and natural mother are not the same.
   */
  GestationalMother: v3_RoleCode_GestationalMother,
  /**
   * The player of the role is the father of the scoping person's grandparent.
   */
  GreatGrandfather: v3_RoleCode_GreatGrandfather,
  /**
   * The player of the role is the mother of the scoping person's grandparent.
   */
  GreatGrandmother: v3_RoleCode_GreatGrandmother,
  /**
   * The player of the role is a parent of the scoping person's grandparent.
   */
  GreatGrandparent: v3_RoleCode_GreatGrandparent,
  /**
   * The player of the role is the father of the scoping person's mother or father.
   */
  Grandfather: v3_RoleCode_Grandfather,
  /**
   * The player of the role is the mother of the scoping person's mother or father.
   */
  Grandmother: v3_RoleCode_Grandmother,
  /**
   * The player of the role is a child of the scoping person's son or daughter.
   */
  Grandchild: v3_RoleCode_Grandchild,
  /**
   * The player of the role is a daughter of the scoping person's son or daughter.
   */
  Granddaughter: v3_RoleCode_Granddaughter,
  /**
   * The player of the role is a son of the scoping person's son or daughter.
   */
  Grandson: v3_RoleCode_Grandson,
  /**
   * The player of the role is a parent of the scoping person's mother or father.
   */
  Grandparent: v3_RoleCode_Grandparent,
  /**
   * The player of the role is a male related to the scoping entity by sharing only one biological parent.
   */
  HalfBrother: v3_RoleCode_HalfBrother,
  /**
   * The player of the role is related to the scoping entity by sharing only one biological parent.
   */
  HalfSibling: v3_RoleCode_HalfSibling,
  /**
   * The player of the role is a female related to the scoping entity by sharing only one biological parent.
   */
  HalfSister: v3_RoleCode_HalfSister,
  /**
   * The player of the role is a man joined to a woman (scoping person) in marriage.
   */
  Husband: v3_RoleCode_Husband,
  /**
   * A relationship between an individual and a member of their spousal partner's immediate family.
   */
  Inlaw: v3_RoleCode_Inlaw,
  /**
   * The scoper and player are offspring of the same egg-sperm pair.
   */
  IdenticalTwin: v3_RoleCode_IdenticalTwin,
  /**
   * The male scoper is an offspring of the same egg-sperm pair as the male player.
   */
  IdenticalTwinBrother: v3_RoleCode_IdenticalTwinBrother,
  /**
   * The female scoper is an offspring of the same egg-sperm pair as the female player.
   */
  IdenticalTwinSister: v3_RoleCode_IdenticalTwinSister,
  /**
   * Description:The player of the role is a biological sister of the scoping person's biological mother.
   */
  MaternalAunt: v3_RoleCode_MaternalAunt,
  /**
   * Description:The player of the role is a biological relative of the scoping person descended from a common ancestor on the player's mother's side, such as a grandparent, by two or more steps in a diverging line.
   */
  MaternalCousin: v3_RoleCode_MaternalCousin,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological mother's parent.
   */
  MaternalGreatGrandfather: v3_RoleCode_MaternalGreatGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological mother's parent.
   */
  MaternalGreatGrandmother: v3_RoleCode_MaternalGreatGrandmother,
  /**
   * Description:The player of the role is a biological parent of the scoping person's biological mother's parent.
   */
  MaternalGreatGrandparent: v3_RoleCode_MaternalGreatGrandparent,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological mother.
   */
  MaternalGrandfather: v3_RoleCode_MaternalGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological mother.
   */
  MaternalGrandmother: v3_RoleCode_MaternalGrandmother,
  /**
   * Description:The player of the role is the biological parent of the scoping person's biological mother.
   */
  MaternalGrandparent: v3_RoleCode_MaternalGrandparent,
  /**
   * The player of the role is a female who conceives, gives birth to, or raises and nurtures the scoping entity (child).
   */
  Mother: v3_RoleCode_Mother,
  /**
   * The player of the role (parent) who is a female state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterMother: v3_RoleCode_FosterMother,
  /**
   * The player of the role is the mother of the scoping person's husband or wife.
   */
  MotherInLaw: v3_RoleCode_MotherInLaw,
  /**
   * Description:The player of the role is a biological brother of the scoping person's biological mother.
   */
  MaternalUncle: v3_RoleCode_MaternalUncle,
  /**
   * The player of the role is a male having the same biological parents as the scoping entity.
   */
  NaturalBrother: v3_RoleCode_NaturalBrother,
  /**
   * The player of the role is an offspring of the scoping entity as determined by birth.
   */
  NaturalChild: v3_RoleCode_NaturalChild,
  /**
   * The player of the role is a son of the scoping person's brother or sister or of the brother or sister of the 	scoping person's spouse.
   */
  Nephew: v3_RoleCode_Nephew,
  /**
   * The player of the role is a male who begets the scoping entity (child).
   */
  NaturalFather: v3_RoleCode_NaturalFather,
  /**
   * Indicates the biologic male parent of a fetus.
   */
  NaturalFatherOfFetus: v3_RoleCode_NaturalFatherOfFetus,
  /**
   * The player of the role is a daughter of the scoping person's brother or sister or of the brother or sister of the 	scoping person's spouse.
   */
  Niece: v3_RoleCode_Niece,
  /**
   * The player of the role is a child of scoping person's brother or sister or of the brother or sister of the 	scoping person's spouse.
   */
  NieceNephew: v3_RoleCode_NieceNephew,
  /**
   * The player of the role is a female who conceives or gives birth to the scoping entity (child).
   */
  NaturalMother: v3_RoleCode_NaturalMother,
  /**
   * The player is the biologic female parent of the scoping fetus.
   */
  NaturalMotherOfFetus: v3_RoleCode_NaturalMotherOfFetus,
  /**
   * natural parent
   */
  NaturalParent: v3_RoleCode_NaturalParent,
  /**
   * The player of the role has both biological parents in common with the scoping entity.
   */
  NaturalSibling: v3_RoleCode_NaturalSibling,
  /**
   * The player of the role is a female having the same biological parents as the scoping entity.
   */
  NaturalSister: v3_RoleCode_NaturalSister,
  /**
   * Description:The player of the role is a biological sister of the scoping person's biological father.
   */
  PaternalAunt: v3_RoleCode_PaternalAunt,
  /**
   * Description:The player of the role is a biological relative of the scoping person descended from a common ancestor on the player's father's side, such as a grandparent, by two or more steps in a diverging line.
   */
  PaternalCousin: v3_RoleCode_PaternalCousin,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological father's parent.
   */
  PaternalGreatGrandfather: v3_RoleCode_PaternalGreatGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological father's parent.
   */
  PaternalGreatGrandmother: v3_RoleCode_PaternalGreatGrandmother,
  /**
   * Description:The player of the role is a biological parent of the scoping person's biological father's parent.
   */
  PaternalGreatGrandparent: v3_RoleCode_PaternalGreatGrandparent,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological father.
   */
  PaternalGrandfather: v3_RoleCode_PaternalGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological father.
   */
  PaternalGrandmother: v3_RoleCode_PaternalGrandmother,
  /**
   * Description:The player of the role is the biological parent of the scoping person's biological father.
   */
  PaternalGrandparent: v3_RoleCode_PaternalGrandparent,
  /**
   * The player of the role is one who begets, gives birth to, or nurtures and raises the scoping entity (child).
   */
  Parent: v3_RoleCode_Parent,
  /**
   * The player of the role (parent) who is a state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterParent: v3_RoleCode_FosterParent,
  /**
   * The player of the role is the parent of scoping person's husband or wife.
   */
  ParentInLaw: v3_RoleCode_ParentInLaw,
  /**
   * Description:The player of the role is a biological brother of the scoping person's biological father.
   */
  PaternalUncle: v3_RoleCode_PaternalUncle,
  /**
   * The player of the role shares one or both parents in common with the scoping entity.
   */
  Sibling: v3_RoleCode_Sibling,
  /**
   * The player of the role is: (1) a sibling of the scoping person's spouse, or (2) the spouse of the scoping person's sibling, or (3) the spouse of a sibling of the scoping person's spouse.
   */
  SiblingInLaw: v3_RoleCode_SiblingInLaw,
  /**
   * A person who is important to one's well being; especially a spouse or one in a similar relationship.  (The player is the one who is important)
   */
  SignificantOther: v3_RoleCode_SignificantOther,
  /**
   * The player of the role is a female sharing one or both parents in common with the scoping entity.
   */
  Sister: v3_RoleCode_Sister,
  /**
   * The player of the role is: (1) a sister of the scoping person's spouse, or (2) the wife of the scoping person's brother, or (3) the wife of a brother of the scoping person's spouse.
   */
  SisterInLaw: v3_RoleCode_SisterInLaw,
  /**
   * The player of the role is a male offspring of the scoping entity (parent).
   */
  NaturalSon: v3_RoleCode_NaturalSon,
  /**
   * The player of the role is a male child taken into a family through legal means and raised by the scoping person (parent) as his or her own child.
   */
  AdoptedSon: v3_RoleCode_AdoptedSon,
  /**
   * Description: The player of the role is a male child (of any type) of scoping entity (parent)
   */
  Son: v3_RoleCode_Son,
  /**
   * The player of the role is a male child receiving parental care and nurture from the scoping person (parent) but not related to him or her through legal or blood ties.
   */
  FosterSon: v3_RoleCode_FosterSon,
  /**
   * The player of the role is the husband of scoping person's daughter.
   */
  SonInLaw: v3_RoleCode_SonInLaw,
  /**
   * The player of the role is a marriage partner of the scoping person.
   */
  Spouse: v3_RoleCode_Spouse,
  /**
   * The player of the role is a son of the scoping person's stepparent.
   */
  Stepbrother: v3_RoleCode_Stepbrother,
  /**
   * The player of the role is a child of the scoping person's spouse by a previous union.
   */
  StepChild: v3_RoleCode_StepChild,
  /**
   * The player of the role is a daughter of the scoping person's spouse by a previous union.
   */
  Stepdaughter: v3_RoleCode_Stepdaughter,
  /**
   * The player of the role is the husband of scoping person's mother and not the scoping person's natural father.
   */
  Stepfather: v3_RoleCode_Stepfather,
  /**
   * The player of the role is the wife of scoping person's father and not the scoping person's natural mother.
   */
  Stepmother: v3_RoleCode_Stepmother,
  /**
   * The player of the role is the spouse of the scoping person's parent and not the scoping person's natural parent.
   */
  StepParent: v3_RoleCode_StepParent,
  /**
   * The player of the role is a child of the scoping person's stepparent.
   */
  StepSibling: v3_RoleCode_StepSibling,
  /**
   * The player of the role is a daughter of the scoping person's stepparent.
   */
  Stepsister: v3_RoleCode_Stepsister,
  /**
   * The player of the role is a son of the scoping person's spouse by a previous union.
   */
  Stepson: v3_RoleCode_Stepson,
  /**
   * The scoper and player were carried in the same womb and shared common biological parents.
   */
  Twin: v3_RoleCode_Twin,
  /**
   * The scoper was carried in the same womb as the male player and shares common biological parents.
   */
  TwinBrother: v3_RoleCode_TwinBrother,
  /**
   * The scoper was carried in the same womb as the female player and shares common biological parents.
   */
  TwinSister: v3_RoleCode_TwinSister,
  /**
   * The player of the role is a brother of the scoping person's mother or father.
   */
  Uncle: v3_RoleCode_Uncle,
  /**
   * The player of the role is a woman joined to a man (scoping person) in marriage.
   */
  Wife: v3_RoleCode_Wife,
};
const v3_GenderStatus_Intact: Coding = {
  code: 'I',
  display: 'Intact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GenderStatus',
};
const v3_GenderStatus_Neutered: Coding = {
  code: 'N',
  display: 'Neutered',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GenderStatus',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3GenderStatus = {
  /**
   * Reproductively intact
   */
  Intact: v3_GenderStatus_Intact,
  /**
   * Reproductively neutered
   */
  Neutered: v3_GenderStatus_Neutered,
};
/**
 *  Supports communication of purpose of use at a general level.
 */
export const V3GeneralPurposeOfUse = {
  /**
   * To perform one or more operations on information for conducting activities related to coverage under a program or policy.
   */
  CoverageUnderPolicyOrProgram: v3_ActReason_CoverageUnderPolicyOrProgram,
  /**
   * To perform one or more operations on information for provision of immediately needed health care for an emergent condition.
   */
  EmergencyTreatment: v3_ActReason_EmergencyTreatment,
  /**
   * To perform one or more operations on information for marketing services and products related to health care.
   */
  HealthcareMarketing: v3_ActReason_HealthcareMarketing,
  /**
   * To perform one or more operations on information used for conducting administrative and contractual activities related to the provision of health care.
   */
  HealthcareOperations: v3_ActReason_HealthcareOperations,
  /**
   * To perform one or more operations on information for conducting financial or contractual activities related to payment for provision of health care.
   */
  HealthcarePayment: v3_ActReason_HealthcarePayment,
  /**
   * To perform one or more operations on information for conducting scientific investigations to obtain health care knowledge.  Use of the data iincludes basic and applied research such as biomedical, population origin or ancestry, translational research, and disease, discipline, specialty specific healthcare research and clinical trial research.
   */
  HealthcareResearch: v3_ActReason_HealthcareResearch,
  /**
   * To perform one or more operations on information in response to a patient's request.
   */
  PatientRequested: v3_ActReason_PatientRequested,
  /**
   * To perform one or more operations on information for conducting public health activities, such as the reporting of notifiable conditions.
   */
  PublicHealth: v3_ActReason_PublicHealth,
  /**
   * To perform one or more operations on information for provision of health care.
   */
  Treatment: v3_ActReason_Treatment,
};
const v3_GTSAbbreviation_GTSAbbreviationHolidaysChristianRoman: Coding = {
  code: '_GTSAbbreviationHolidaysChristianRoman',
  display: 'GTSAbbreviationHolidaysChristianRoman',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JB: Coding = {
  code: 'JB',
  display: 'JB',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JE: Coding = {
  code: 'JE',
  display: 'JE',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_GTSAbbreviationHolidays: Coding = {
  code: 'JH',
  display: 'GTSAbbreviationHolidays',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHCHREAS: Coding = {
  code: 'JHCHREAS',
  display: 'JHCHREAS',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHCHRGFR: Coding = {
  code: 'JHCHRGFR',
  display: 'JHCHRGFR',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHCHRNEW: Coding = {
  code: 'JHCHRNEW',
  display: 'JHCHRNEW',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHCHRPEN: Coding = {
  code: 'JHCHRPEN',
  display: 'JHCHRPEN',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHCHRXME: Coding = {
  code: 'JHCHRXME',
  display: 'JHCHRXME',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHCHRXMS: Coding = {
  code: 'JHCHRXMS',
  display: 'JHCHRXMS',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_TheNetherlandsNationalHolidays: Coding = {
  code: 'JHNNL',
  display: 'The Netherlands National Holidays',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_LiberationDayMay5EveryFiveYears: Coding = {
  code: 'JHNNLLD',
  display: 'Liberation day (May 5 every five years)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_QueenQuoteSDayApril30: Coding = {
  code: 'JHNNLQD',
  display: "Queen's day (April 30)",
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_SinterklaasDecember5: Coding = {
  code: 'JHNNLSK',
  display: 'Sinterklaas (December 5)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_GTSAbbreviationHolidaysUSNational: Coding = {
  code: 'JHNUS',
  display: 'GTSAbbreviationHolidaysUSNational',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHNUSCLM: Coding = {
  code: 'JHNUSCLM',
  display: 'JHNUSCLM',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHNUSIND: Coding = {
  code: 'JHNUSIND',
  display: 'JHNUSIND',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHNUSIND1: Coding = {
  code: 'JHNUSIND1',
  display: 'JHNUSIND1',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHNUSIND5: Coding = {
  code: 'JHNUSIND5',
  display: 'JHNUSIND5',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHNUSLBR: Coding = {
  code: 'JHNUSLBR',
  display: 'JHNUSLBR',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHNUSMEM: Coding = {
  code: 'JHNUSMEM',
  display: 'JHNUSMEM',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHNUSMEM5: Coding = {
  code: 'JHNUSMEM5',
  display: 'JHNUSMEM5',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHNUSMEM6: Coding = {
  code: 'JHNUSMEM6',
  display: 'JHNUSMEM6',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHNUSMLK: Coding = {
  code: 'JHNUSMLK',
  display: 'JHNUSMLK',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHNUSPRE: Coding = {
  code: 'JHNUSPRE',
  display: 'JHNUSPRE',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHNUSTKS: Coding = {
  code: 'JHNUSTKS',
  display: 'JHNUSTKS',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHNUSTKS5: Coding = {
  code: 'JHNUSTKS5',
  display: 'JHNUSTKS5',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
const v3_GTSAbbreviation_JHNUSVET: Coding = {
  code: 'JHNUSVET',
  display: 'JHNUSVET',
  system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation',
};
/**
 *   Open Issue:
 * It appears that the printnames are suboptimal and should be improved for many of the existing codes.
 */
export const V3GTSAbbreviation = {
  /**
   * Christian Holidays (Roman/Gregorian [Western] Tradition.)
   */
  GTSAbbreviationHolidaysChristianRoman: v3_GTSAbbreviation_GTSAbbreviationHolidaysChristianRoman,
  /**
   * Every morning at institution specified times.
   */
  AM: v3_GTSAbbreviation_AM,
  /**
   * At bedtime (institution specified time).
   */
  AtBedtime: v3_GTSAbbreviation_AtBedtime,
  /**
   * Two times a day at institution specified time
   */
  BID: v3_GTSAbbreviation_BID,
  /**
   * Regular business days (Monday to Friday excluding holidays)
   */
  JB: v3_GTSAbbreviation_JB,
  /**
   * Regular weekends (Saturday and Sunday excluding holidays)
   */
  JE: v3_GTSAbbreviation_JE,
  /**
   * Holidays
   */
  GTSAbbreviationHolidays: v3_GTSAbbreviation_GTSAbbreviationHolidays,
  /**
   * Easter Sunday.  The Easter date is a rather complex calculation based on Astronomical tables describing full moon dates.  Details can be found at [http://www.assa.org.au/edm.html, and http://aa.usno.navy.mil/AA/faq/docs/easter.html].  Note that the Christian Orthodox Holidays are based on the Julian calendar.
   */
  JHCHREAS: v3_GTSAbbreviation_JHCHREAS,
  /**
   * Good Friday, is the Friday right before Easter Sunday.
   */
  JHCHRGFR: v3_GTSAbbreviation_JHCHRGFR,
  /**
   * New Year's Day (January 1)
   */
  JHCHRNEW: v3_GTSAbbreviation_JHCHRNEW,
  /**
   * Pentecost Sunday, is seven weeks after Easter (the 50th day of Easter).
   */
  JHCHRPEN: v3_GTSAbbreviation_JHCHRPEN,
  /**
   * Christmas Eve (December 24)
   */
  JHCHRXME: v3_GTSAbbreviation_JHCHRXME,
  /**
   * Christmas Day (December 25)
   */
  JHCHRXMS: v3_GTSAbbreviation_JHCHRXMS,
  /**
   * Description:The Netherlands National Holidays.
   */
  TheNetherlandsNationalHolidays: v3_GTSAbbreviation_TheNetherlandsNationalHolidays,
  /**
   * Description:Liberation day  (May 5 every five years)
   */
  LiberationDayMay5EveryFiveYears: v3_GTSAbbreviation_LiberationDayMay5EveryFiveYears,
  /**
   * Description:Queen's day (April 30)
   */
  QueenQuoteSDayApril30: v3_GTSAbbreviation_QueenQuoteSDayApril30,
  /**
   * Description:Sinterklaas (December 5)
   */
  SinterklaasDecember5: v3_GTSAbbreviation_SinterklaasDecember5,
  /**
   * United States National Holidays (public holidays for federal employees established by U.S. Federal law 5 U.S.C. 6103).
   */
  GTSAbbreviationHolidaysUSNational: v3_GTSAbbreviation_GTSAbbreviationHolidaysUSNational,
  /**
   * Columbus Day, the second Monday in October.
   */
  JHNUSCLM: v3_GTSAbbreviation_JHNUSCLM,
  /**
   * Independence Day (4th of July)
   */
  JHNUSIND: v3_GTSAbbreviation_JHNUSIND,
  /**
   * Alternative Monday after 4th of July Weekend [5 U.S.C. 6103(b)].
   */
  JHNUSIND1: v3_GTSAbbreviation_JHNUSIND1,
  /**
   * Alternative Friday before 4th of July Weekend [5 U.S.C. 6103(b)].
   */
  JHNUSIND5: v3_GTSAbbreviation_JHNUSIND5,
  /**
   * Labor Day, the first Monday in September.
   */
  JHNUSLBR: v3_GTSAbbreviation_JHNUSLBR,
  /**
   * Memorial Day, the last Monday in May.
   */
  JHNUSMEM: v3_GTSAbbreviation_JHNUSMEM,
  /**
   * Friday before Memorial Day Weekend
   */
  JHNUSMEM5: v3_GTSAbbreviation_JHNUSMEM5,
  /**
   * Saturday of Memorial Day Weekend
   */
  JHNUSMEM6: v3_GTSAbbreviation_JHNUSMEM6,
  /**
   * Dr. Martin Luther King, Jr. Day, the third Monday in January.
   */
  JHNUSMLK: v3_GTSAbbreviation_JHNUSMLK,
  /**
   * Washington's Birthday (Presidential Day) the third Monday in February.
   */
  JHNUSPRE: v3_GTSAbbreviation_JHNUSPRE,
  /**
   * Thanksgiving Day, the fourth Thursday in November.
   */
  JHNUSTKS: v3_GTSAbbreviation_JHNUSTKS,
  /**
   * Friday after Thanksgiving.
   */
  JHNUSTKS5: v3_GTSAbbreviation_JHNUSTKS5,
  /**
   * Veteran's Day, November 11.
   */
  JHNUSVET: v3_GTSAbbreviation_JHNUSVET,
  /**
   * Monthly at institution specified time.
   */
  Monthly: v3_GTSAbbreviation_Monthly,
  /**
   * Every afternoon at institution specified times.
   */
  PM: v3_GTSAbbreviation_PM,
  /**
   * Every hour at institution specified times.
   */
  EveryHour: v3_GTSAbbreviation_EveryHour,
  /**
   * Every 2 hours at institution specified times.
   */
  Every2Hours: v3_GTSAbbreviation_Every2Hours,
  /**
   * Every 3 hours at institution specified times.
   */
  Every3Hours: v3_GTSAbbreviation_Every3Hours,
  /**
   * Every 4 hours at institution specified time
   */
  Q4H: v3_GTSAbbreviation_Q4H,
  /**
   * Every 6 hours at institution specified time
   */
  Q6H: v3_GTSAbbreviation_Q6H,
  /**
   * Every 8 hours at institution specified times.
   */
  Every8Hours: v3_GTSAbbreviation_Every8Hours,
  /**
   * Every day at institution specified times.
   */
  QD: v3_GTSAbbreviation_QD,
  /**
   * Four times a day at institution specified time
   */
  QID: v3_GTSAbbreviation_QID,
  /**
   * Every other day at institution specified times.
   */
  QOD: v3_GTSAbbreviation_QOD,
  /**
   * Three times a day at institution specified time
   */
  TID: v3_GTSAbbreviation_TID,
  /**
   * Weekly at institution specified time.
   */
  Weekly: v3_GTSAbbreviation_Weekly,
};
const v3_hl7ApprovalStatus_AffiliateBallotDSTU: Coding = {
  code: 'affd',
  display: 'affiliate ballot - DSTU',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_AffiliateBallotInformative: Coding = {
  code: 'affi',
  display: 'affiliate ballot - informative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_AffiliateBallotNormative: Coding = {
  code: 'affn',
  display: 'affiliate ballot - normative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_ApprovedAffiliateDSTU: Coding = {
  code: 'appad',
  display: 'approved affiliate DSTU',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_ApprovedAffiliateInformative: Coding = {
  code: 'appai',
  display: 'approved affiliate informative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_ApprovedAffiliateNormative: Coding = {
  code: 'appan',
  display: 'approved affiliate normative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_ApprovedDSTU: Coding = {
  code: 'appd',
  display: 'approved DSTU',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_ApprovedInformative: Coding = {
  code: 'appi',
  display: 'approved informative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_ApprovedNormative: Coding = {
  code: 'appn',
  display: 'approved normative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_Draft: Coding = {
  code: 'draft',
  display: 'draft',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_LocalizedAdaptation: Coding = {
  code: 'loc',
  display: 'localized adaptation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_MembershipBallotDSTU: Coding = {
  code: 'memd',
  display: 'membership ballot - DSTU',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_MembershipBallotInformative: Coding = {
  code: 'memi',
  display: 'membership ballot - informative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_MembershipBallotNormative: Coding = {
  code: 'memn',
  display: 'membership ballot - normative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_NonStandardAvailableForUse: Coding = {
  code: 'ns',
  display: 'non-standard - available for use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_Proposal: Coding = {
  code: 'prop',
  display: 'proposal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_Reference: Coding = {
  code: 'ref',
  display: 'reference',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
const v3_hl7ApprovalStatus_Withdrawn: Coding = {
  code: 'wd',
  display: 'withdrawn',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ApprovalStatus',
};
/**
 *   Description:
 * Codes for concepts describing the approval level of HL7 artifacts.  This code system reflects the concepts expressed in HL7's Governance & Operations Manual (GOM) past and present.
 */
export const V3Hl7ApprovalStatus = {
  /**
   * Description: Content that is being presented to an international affiliate for consideration as a realm-specific draft standard for trial use.
   */
  AffiliateBallotDSTU: v3_hl7ApprovalStatus_AffiliateBallotDSTU,
  /**
   * Description: Content that is being presented to an international affiliate for consideration as a realm-specific informative standard.
   */
  AffiliateBallotInformative: v3_hl7ApprovalStatus_AffiliateBallotInformative,
  /**
   * Description: Content that is being presented to an international affiliate for consideration as a realm-specific normative standard.
   */
  AffiliateBallotNormative: v3_hl7ApprovalStatus_AffiliateBallotNormative,
  /**
   * Description: Content that has passed ballot as a realm-specific draft standard for trial use.
   */
  ApprovedAffiliateDSTU: v3_hl7ApprovalStatus_ApprovedAffiliateDSTU,
  /**
   * Description: Content that has passed ballot as a realm-specific informative standard.
   */
  ApprovedAffiliateInformative: v3_hl7ApprovalStatus_ApprovedAffiliateInformative,
  /**
   * Description: Content that has passed ballot as a realm-specific normative standard
   */
  ApprovedAffiliateNormative: v3_hl7ApprovalStatus_ApprovedAffiliateNormative,
  /**
   * Description: Content that has passed ballot as a draft standard for trial use.
   */
  ApprovedDSTU: v3_hl7ApprovalStatus_ApprovedDSTU,
  /**
   * Description: Content that has passed ballot as a normative standard.
   */
  ApprovedInformative: v3_hl7ApprovalStatus_ApprovedInformative,
  /**
   * Description: Content that has passed ballot as a normative standard.
   */
  ApprovedNormative: v3_hl7ApprovalStatus_ApprovedNormative,
  /**
   * Description: Content that is under development and is not intended to be used.
   */
  Draft: v3_hl7ApprovalStatus_Draft,
  /**
   * Description: Content that represents an adaption of a implementable balloted material to represent the needs or capabilities of a particular installation.
   */
  LocalizedAdaptation: v3_hl7ApprovalStatus_LocalizedAdaptation,
  /**
   * Description: Content prepared by a committee and submitted for membership consideration as a draft standard for trial use.
   */
  MembershipBallotDSTU: v3_hl7ApprovalStatus_MembershipBallotDSTU,
  /**
   * Description: Content prepared by a committee and submitted for membership consideration as an informative standard.
   */
  MembershipBallotInformative: v3_hl7ApprovalStatus_MembershipBallotInformative,
  /**
   * Description: Content prepared by a committee and submitted for membership consideration as a normative standard.
   */
  MembershipBallotNormative: v3_hl7ApprovalStatus_MembershipBallotNormative,
  /**
   * Description: Content developed independently by an organization or individual that is declared to be 'usable' but for which there is no present intention to submit through the standards submission and review process.
   */
  NonStandardAvailableForUse: v3_hl7ApprovalStatus_NonStandardAvailableForUse,
  /**
   * Description: Content submitted to a committee for consideration for future inclusion in the standard.
   */
  Proposal: v3_hl7ApprovalStatus_Proposal,
  /**
   * Description: Content intended to support other content that is subject to approval, but which is not itself subject to formal approval.
   */
  Reference: v3_hl7ApprovalStatus_Reference,
  /**
   * Description: Content that represents an item that was at one point a normative or informative standard, but was subsequently withdrawn.
   */
  Withdrawn: v3_hl7ApprovalStatus_Withdrawn,
};
const v3_hl7CMETAttribution_Contact: Coding = {
  code: 'contact',
  display: 'contact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7CMETAttribution',
};
const v3_hl7CMETAttribution_Identified: Coding = {
  code: 'identified',
  display: 'identified',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7CMETAttribution',
};
const v3_hl7CMETAttribution_IdentifiedConfirmable: Coding = {
  code: 'identified-confirmable',
  display: 'identified-confirmable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7CMETAttribution',
};
const v3_hl7CMETAttribution_IdentifiedInformational: Coding = {
  code: 'identified-informational',
  display: 'identified-informational',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7CMETAttribution',
};
const v3_hl7CMETAttribution_Informational: Coding = {
  code: 'informational',
  display: 'informational',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7CMETAttribution',
};
const v3_hl7CMETAttribution_Minimal: Coding = {
  code: 'minimal',
  display: 'minimal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7CMETAttribution',
};
const v3_hl7CMETAttribution_Universal: Coding = {
  code: 'universal',
  display: 'universal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7CMETAttribution',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3Hl7CMETAttribution = {
  /**
   * Description: Provides sufficient information to allow the object identified to be contacted. This is likely to have the content of identified and confirmable plus telephone number.
   */
  Contact: v3_hl7CMETAttribution_Contact,
  /**
   * Description: This variant is a proper subset of universal and is intended to provide sufficient information to identify the object(s) modeled by the CMET. This variant is only suitable for use within TIGHTLY COUPLED SYSTEMS ONLY. This variant provides ONLY the ID (and code where applicable) and Name. Other variants may not be substituted at runtime.
   */
  Identified: v3_hl7CMETAttribution_Identified,
  /**
   * Description: This extends the identified variant by adding just sufficient additional information to allow the identity of object modeled to be confirmed by a number of corroborating items of data; for instance a patient's date of birth and current address. However, specific contact information, such as telephone number, are not viewed as confirming information.
   */
  IdentifiedConfirmable: v3_hl7CMETAttribution_IdentifiedConfirmable,
  /**
   * Description: Generally the same information content as "contactable" but using new "informational" CMETs as dependant CMETs. This flavor allows expression of the CMET when non-focal class information is not known.
   */
  IdentifiedInformational: v3_hl7CMETAttribution_IdentifiedInformational,
  /**
   * Description: Generally the same information content as "contactable", but with required (not mandatory) ids on entry point class. This flavor allows expression of the CMET even when mandatory information is not known.
   */
  Informational: v3_hl7CMETAttribution_Informational,
  /**
   * Description: Provides more than identified, but not as much as universal. There are not expected to be many of these.
   */
  Minimal: v3_hl7CMETAttribution_Minimal,
  /**
   * Description: This variant includes all attributes and associations present in the R-MIM. Any of non-mandatory and non-required attributes and/or associations may be present or absent, as permitted in the cardinality constraints.
   */
  Universal: v3_hl7CMETAttribution_Universal,
};
const v3_HL7ContextConductionStyle_ConductionIndicatorBased: Coding = {
  code: 'C',
  display: 'conduction-indicator-based',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7ContextConductionStyle',
};
const v3_HL7ContextConductionStyle_Inferred: Coding = {
  code: 'I',
  display: 'inferred',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7ContextConductionStyle',
};
const v3_HL7ContextConductionStyle_VocabularyBased: Coding = {
  code: 'V',
  display: 'vocabulary-based',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7ContextConductionStyle',
};
/**
 * The styles of context conduction usable by relationships within a static model derived from tyhe HL7 Reference Information Model.
 */
export const V3HL7ContextConductionStyle = {
  /**
   * Definition: Context conduction is defined using the contextConductionCode and contextConductionInd attributes on ActRelationship and Participation.
   *
   *
   *                            UsageNotes: This approach is deprecated as of March, 2010.
   */
  ConductionIndicatorBased: v3_HL7ContextConductionStyle_ConductionIndicatorBased,
  /**
   * Definition: Context conduction is not explicitly defined.  The recipient of an instance must infer conduction based on the semantics of the model and what is deemed "reasonable".
   *
   *
   *                            UsageNotes: Because this approach can lead to variation in instance interpretation, its use is discouraged.
   */
  Inferred: v3_HL7ContextConductionStyle_Inferred,
  /**
   * Definition: Context conduction is defined using the ActRelationship.blockedContextActRelationshipType and blockedContextParticipationType attributes and the "conductible" property on the ActRelationshipType and ParticipationType code systems.
   */
  VocabularyBased: v3_HL7ContextConductionStyle_VocabularyBased,
};
const v3_hl7ITSType_UniversalModelingLanguage: Coding = {
  code: 'UML',
  display: 'universal modeling language',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ITSType',
};
const v3_hl7ITSType_ExtensibleMarkupLanguage: Coding = {
  code: 'XML',
  display: 'extensible markup language',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ITSType',
};
/**
 *   Description:
 * Codes identifying types of HL7 Implementation Technology Specifications
 */
export const V3Hl7ITSType = {
  /**
   * Description: ITS based on the Universal Modeling Language.
   */
  UniversalModelingLanguage: v3_hl7ITSType_UniversalModelingLanguage,
  /**
   * Description: ITS based on the Extensible Markup Language.
   */
  ExtensibleMarkupLanguage: v3_hl7ITSType_ExtensibleMarkupLanguage,
};
const v3_hl7ITSVersionCode_XMLV1PR1: Coding = {
  code: 'XMLV1PR1',
  display: 'XMLV1PR1',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7ITSVersionCode',
};
/**
 *  HL7 implementation technology specification versions. These codes will document the ITS type and version for message encoding. The code will appear in the instances based upon rules expressed in the ITS, and do not appear in the abstract message, either as it is presented to received from the ITS.
 */
export const V3Hl7ITSVersionCode = {
  /**
   * XML ITS version 1 pre-release 1.
   */
  XMLV1PR1: v3_hl7ITSVersionCode_XMLV1PR1,
};
const v3_hl7PublishingDomain_AccountingAndBilling: Coding = {
  code: 'AB',
  display: 'accounting & billing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_TriggerEventControlActInfrastructure: Coding = {
  code: 'AI',
  display: 'trigger event control act infrastructure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_ArtificialListingForTestPurposesFauxDomainForTesting: Coding = {
  code: 'AL',
  display: 'artificial listing for test purposes - faux Domain for testing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_BloodTissueAndOrgan: Coding = {
  code: 'BB',
  display: 'blood tissue and organ',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_ClinicalDocumentArchitecture: Coding = {
  code: 'CD',
  display: 'clinical document architecture',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_ClinicalGenomics: Coding = {
  code: 'CG',
  display: 'clinical genomics',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_TransmissionInfrastructure: Coding = {
  code: 'CI',
  display: 'transmission infrastructure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_Coverage: Coding = {
  code: 'CO',
  display: 'coverage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_CommonProductModel: Coding = {
  code: 'CP',
  display: 'common product model',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_ClaimsAndReimbursement: Coding = {
  code: 'CR',
  display: 'claims and reimbursement',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_ClinicalStatement: Coding = {
  code: 'CS',
  display: 'clinical statement',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_CommonTypes: Coding = {
  code: 'CT',
  display: 'common types',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_DummyDomain: Coding = {
  code: 'DD',
  display: 'dummy domain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_DiagnosticImaging: Coding = {
  code: 'DI',
  display: 'diagnostic imaging',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_DecisionSupport: Coding = {
  code: 'DS',
  display: 'decision support',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_EmergencyMedicalServices: Coding = {
  code: 'EM',
  display: 'emergency medical services',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_ImagingIntegration: Coding = {
  code: 'II',
  display: 'imaging integration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_Immunization: Coding = {
  code: 'IZ',
  display: 'immunization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_Laboratory: Coding = {
  code: 'LB',
  display: 'laboratory',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_Medication: Coding = {
  code: 'ME',
  display: 'medication',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_MasterfileInfrastructure: Coding = {
  code: 'MI',
  display: 'masterfile infrastructure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_MaterialsManagement: Coding = {
  code: 'MM',
  display: 'materials management',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_MedicalRecords: Coding = {
  code: 'MR',
  display: 'medical records',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_SharedMessages: Coding = {
  code: 'MT',
  display: 'shared messages',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_Observations: Coding = {
  code: 'OB',
  display: 'observations',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_OrdersAndObservations: Coding = {
  code: 'OO',
  display: 'orders & observations',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_Orders: Coding = {
  code: 'OR',
  display: 'orders',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_PatientAdministration: Coding = {
  code: 'PA',
  display: 'patient administration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_CareProvision: Coding = {
  code: 'PC',
  display: 'care provision',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_PublicHealth: Coding = {
  code: 'PH',
  display: 'public health',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_PersonnelManagement: Coding = {
  code: 'PM',
  display: 'personnel management',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_QueryInfrastructure: Coding = {
  code: 'QI',
  display: 'query infrastructure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_QualityMeasures: Coding = {
  code: 'QM',
  display: 'quality measures',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_Registries: Coding = {
  code: 'RG',
  display: 'registries',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_InformativePublicHealth: Coding = {
  code: 'RI',
  display: 'informative public health',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_RegulatedProducts: Coding = {
  code: 'RP',
  display: 'regulated products',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_PublicHealthReporting: Coding = {
  code: 'RR',
  display: 'public health reporting',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_RegulatedStudies: Coding = {
  code: 'RT',
  display: 'regulated studies',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_Pharmacy: Coding = {
  code: 'RX',
  display: 'pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_Scheduling: Coding = {
  code: 'SC',
  display: 'scheduling',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_Specimen: Coding = {
  code: 'SP',
  display: 'specimen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
const v3_hl7PublishingDomain_TherapeuticDevices: Coding = {
  code: 'TD',
  display: 'therapeutic devices',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingDomain',
};
/**
 *   Description:
 * Codes for HL7 publishing domains (specific content area)
 */
export const V3Hl7PublishingDomain = {
  /**
   * Description: Represents the HL7 content "domain" that supports accounting and billing functions - and "provides support for the creation and management of patient billing accounts and the post of financial transactions against patient billing accounts for the purpose of aggregating financial transactions that will be submitted as claims or invoices for reimbursemen"
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  AccountingAndBilling: v3_hl7PublishingDomain_AccountingAndBilling,
  /**
   * Description: Represents the HL7 content "domain" that supports trigger event control act infrastructure - and "covers the alternate structures of the message Trigger Event Control Acts in the HL7 Composite Message."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  TriggerEventControlActInfrastructure: v3_hl7PublishingDomain_TriggerEventControlActInfrastructure,
  /**
   * Description: Represents the HL7 content "domain" that was defined as an "artificial listing" domain to support publication testing.
   */
  ArtificialListingForTestPurposesFauxDomainForTesting: v3_hl7PublishingDomain_ArtificialListingForTestPurposesFauxDomainForTesting,
  /**
   * Description: Represents the HL7 content "domain" that supports blood tissue and organ domain - and "comprises the models, messages, and other artIfacts that are needed to support messaging related to the process of blood, tissue, and organ banking operations such as donations, eligibility, storage, dispense, administration/transfusion, explantation, and implantation. "
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  BloodTissueAndOrgan: v3_hl7PublishingDomain_BloodTissueAndOrgan,
  /**
   * Description: Represents the HL7 content "domain" that supports the clinical document architecture.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  ClinicalDocumentArchitecture: v3_hl7PublishingDomain_ClinicalDocumentArchitecture,
  /**
   * Description: Represents the HL7 content "domain" that supports clinical genomics - and includes " standards to enable the exchange of interrelated clinical and personalized genomic data between interested parties."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  ClinicalGenomics: v3_hl7PublishingDomain_ClinicalGenomics,
  /**
   * Description: Represents the HL7 content "domain" that supports transmission infrastructure - and " is primarily concerned with the data content of exchanges between healthcare applications, the sequence or interrelationships in the flow of messages and the communication of significant application level exceptions or error conditions."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  TransmissionInfrastructure: v3_hl7PublishingDomain_TransmissionInfrastructure,
  /**
   * Description: Represents the HL7 content "domain" that supports Coverage - and provides support for managing health care coverage in the reimbursement system(s).
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  Coverage: v3_hl7PublishingDomain_Coverage,
  /**
   * Description: Represents the HL7 content "domain" that supports the common product model - which "is used to improve the alignment between the different representations of products used within the body of HL7 Version 3 models."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  CommonProductModel: v3_hl7PublishingDomain_CommonProductModel,
  /**
   * Description: Represents the HL7 content "domain" that supports Claims and Reimbursement - and "provides support for Generic, Pharmacy, Preferred Accommodation, Physician, Oral Health Vision Care and Hospital claims for eligibility, authorization, coverage extension, pre-determination, invoice adjudication, payment advice and Statement of Financial Activity (SOFA) Release 3 of this document adds claims messaging support for Physician, Oral Health Vision Care and Hospital claims."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  ClaimsAndReimbursement: v3_hl7PublishingDomain_ClaimsAndReimbursement,
  /**
   * Description: Represents the HL7 content "domain" that supports a common clinical statement pattern - and "is a 'pattern' designed to be used within multiple HL7 Version 3 domain models. This pattern is intended to facilitate the consistent design of communications that convey clinical information to meet specific use cases."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  ClinicalStatement: v3_hl7PublishingDomain_ClinicalStatement,
  /**
   * Description: Represents the HL7 content "domain" that supports common model types - and "are a work product produced by a particular committee for expressing a common, useful and reusable concept."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  CommonTypes: v3_hl7PublishingDomain_CommonTypes,
  /**
   * Description: Represents the HL7 content "domain" that was created to support testing and initial set-up functions.
   */
  DummyDomain: v3_hl7PublishingDomain_DummyDomain,
  /**
   * Description: This domain has been retired in favor of "imaging integration" (II).
   */
  DiagnosticImaging: v3_hl7PublishingDomain_DiagnosticImaging,
  /**
   * Description: Represents the HL7 content "domain" that provides decision support.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  DecisionSupport: v3_hl7PublishingDomain_DecisionSupport,
  /**
   * Description: Represents the HL7 content "domain" that supports Emergency Medical Services.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  EmergencyMedicalServices: v3_hl7PublishingDomain_EmergencyMedicalServices,
  /**
   * Description: Represents the HL7 content "domain" that supports imaging integration - and is "comprises the models, implementation guides, sample documents and images that are needed to illustrate the transformation of DICOM structured reports to CDA Release 2 as well as the creation of CDA diagnostic imaging reports."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  ImagingIntegration: v3_hl7PublishingDomain_ImagingIntegration,
  /**
   * Description: Represents the HL7 content "domain" that supports immunization - and "describes communication of information about immunization: the administration of vaccines (and/or antisera) to individuals to prevent infectious disease."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  Immunization: v3_hl7PublishingDomain_Immunization,
  /**
   * Description: Represents the HL7 content "domain" that supports clinical laboratory functions - and is "comprises the models, messages, and other artifacts that are needed to support messaging related to laboratory tests or observations. "
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  Laboratory: v3_hl7PublishingDomain_Laboratory,
  /**
   * Description: Represents the HL7 content "domain" that supports medication - and  "deals with the description of a medicine for the purposes of messaging information about medicines" and the applications of these descriptions.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  Medication: v3_hl7PublishingDomain_Medication,
  /**
   * Description: Represents the HL7 content "domain" that supports master file infrastructure - and is "comprises the classes and attributes needed to support Master Files and Registries."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  MasterfileInfrastructure: v3_hl7PublishingDomain_MasterfileInfrastructure,
  /**
   * Description: Represents the HL7 content "domain" that supports Materials Management - and is "supports the simple scenario of a Materials Management application sending requests, notifications and queries to an auxiliary application. The intent is to establish a standard for the minimum functionality that is useful and comprehensive enough to explore the important concepts relative to inventory management."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  MaterialsManagement: v3_hl7PublishingDomain_MaterialsManagement,
  /**
   * Description: Represents the HL7 content "domain" that supports medical records - and is "supports clinical document management, and document querying."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  MedicalRecords: v3_hl7PublishingDomain_MedicalRecords,
  /**
   * Description: Represents the HL7 content "domain" that supports shared messages - and "are a work product produced for expressing common, useful and reusable message types."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  SharedMessages: v3_hl7PublishingDomain_SharedMessages,
  /**
   * Description: Represents the HL7 content "domain" that supports observations - and is "comprises the models, messages, and other artifacts that are needed to support messaging related to resulting basic healthcare diagnostic services. "
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  Observations: v3_hl7PublishingDomain_Observations,
  /**
   * Description: Represents the HL7 content "domain" that supports orders and observations - and will provide over-arching support information for the "Orders" (OR) and "Observations" (OB) domains.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  OrdersAndObservations: v3_hl7PublishingDomain_OrdersAndObservations,
  /**
   * Description: Represents the HL7 content "domain" that supports orders - and "comprises the models, messages, and other artifacts that are needed to support messaging related to ordering basic healthcare services."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  Orders: v3_hl7PublishingDomain_Orders,
  /**
   * Description: Represents the HL7 content "domain" that supports Patient Administration - and "defines person and patient demographics and visit information about patients"
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  PatientAdministration: v3_hl7PublishingDomain_PatientAdministration,
  /**
   * Description: Represents the HL7 content "domain" that supports Care Provision - and "addresses the information that is needed for the ongoing care of individuals, populations, and other targets of care."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  CareProvision: v3_hl7PublishingDomain_CareProvision,
  /**
   * Description: Represents the HL7 content "domain" that supports public health - and is "the source of a number of Common Model Element Types (CMET) designed to meet the needs of public health data exchange."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  PublicHealth: v3_hl7PublishingDomain_PublicHealth,
  /**
   * Description: Represents the HL7 content "domain" that supports Personnel Management - and "spans a variety of clinical-administrative information functions associated with the organizations, individuals, animals and devices involved in the delivery and support of healthcare services."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  PersonnelManagement: v3_hl7PublishingDomain_PersonnelManagement,
  /**
   * Description: Represents the HL7 content "domain" that supports query infrastructure - and "specifies the formation of information queries and the responses to these queries to meet the needs of healthcare applications using the HL7 version 3 messaging standard."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  QueryInfrastructure: v3_hl7PublishingDomain_QueryInfrastructure,
  /**
   * Description: Represents the HL7 content "domain" that supports Quality Measures - and "is a standard for representing a health quality measure as an electronic document."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  QualityMeasures: v3_hl7PublishingDomain_QualityMeasures,
  /**
   * Description: Represents the HL7 content "domain" that supports Registries - and "collects HL7 artifacts for administrative  registries."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  Registries: v3_hl7PublishingDomain_Registries,
  /**
   * Description: Represents the HL7 content "domain" that supports Informative Public Health.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  InformativePublicHealth: v3_hl7PublishingDomain_InformativePublicHealth,
  /**
   * Description: Represents the HL7 content "domain" that supports Regulated Products - and "includes standards developed as part of the family of messages targeted for the exchange of information about regulated products and the exchange of the data needed to provide approval for such products."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  RegulatedProducts: v3_hl7PublishingDomain_RegulatedProducts,
  /**
   * Description: Represents the HL7 content "domain" that supports Public Health Reporting - and "includes messages and documents that are specifically designed to support managment, reporting and investigation in the public health context."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  PublicHealthReporting: v3_hl7PublishingDomain_PublicHealthReporting,
  /**
   * Description: Represents the HL7 content "domain" that supports Regulated Studies - and is "includes standards developed as part of the family of messages targeted for the exchange of information about the conduct of regulated studies, and the exchange of the data collected during those studies."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  RegulatedStudies: v3_hl7PublishingDomain_RegulatedStudies,
  /**
   * Description: Represents the HL7 content "domain" that supports pharmacy - and is a "model used to derive message patterns to describe and communicate processes related to medication."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  Pharmacy: v3_hl7PublishingDomain_Pharmacy,
  /**
   * Description: Represents the HL7 content "domain" that supports Scheduling - and "offers a generic set of messages and behavior to implement any number of Scheduling scenarios."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  Scheduling: v3_hl7PublishingDomain_Scheduling,
  /**
   * Description: Represents the HL7 content "domain" that supports Specimen - and "comprises the models and artifacts that are needed to support the creation of messaging related to specimen."
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  Specimen: v3_hl7PublishingDomain_Specimen,
  /**
   * Description: Represents the HL7 content "domain" that supports Therapeutic Devices - and is "comprises the models, messages, and other artifacts that are needed to support messaging related to therapy delivery and observations made by a medical device. "
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   */
  TherapeuticDevices: v3_hl7PublishingDomain_TherapeuticDevices,
};
const v3_hl7PublishingSection_AdministrativeManagement: Coding = {
  code: 'AM',
  display: 'administrative management',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSection',
};
const v3_hl7PublishingSection_HealthAndClinicalManagement: Coding = {
  code: 'HM',
  display: 'health and clinical management',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSection',
};
const v3_hl7PublishingSection_InfrastructureManagement: Coding = {
  code: 'IM',
  display: 'infrastructure management',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSection',
};
const v3_hl7PublishingSection_Unknown: Coding = {
  code: 'UU',
  display: 'unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSection',
};
/**
 *   Description:
 * Codes for HL7 publishing sections (major business categories)
 */
export const V3Hl7PublishingSection = {
  /**
   * Description: Represents the HL7 V3 publishing section that deals with the administration and management of health care activities and organizations.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  AdministrativeManagement: v3_hl7PublishingSection_AdministrativeManagement,
  /**
   * Description: Represents the HL7 V3 publishing section that deals with the health care provision and clinical management.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  HealthAndClinicalManagement: v3_hl7PublishingSection_HealthAndClinicalManagement,
  /**
   * Description: Represents the HL7 V3 publishing section that deals with the definition and management of the computing and communication infrastructure necessary to support health care.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  InfrastructureManagement: v3_hl7PublishingSection_InfrastructureManagement,
  /**
   * Description: Represents the HL7 V3 publishing section that holds specifications that are unassigned - that have not yet been assigned to one of the formal publishing sections.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  Unknown: v3_hl7PublishingSection_Unknown,
};
const v3_hl7PublishingSubSection_Common: Coding = {
  code: 'CO',
  display: 'common',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSubSection',
};
const v3_hl7PublishingSubSection_FinancialInformation: Coding = {
  code: 'FI',
  display: 'financial information',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSubSection',
};
const v3_hl7PublishingSubSection_MessageControl: Coding = {
  code: 'MC',
  display: 'message control',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSubSection',
};
const v3_hl7PublishingSubSection_MasterFile: Coding = {
  code: 'MF',
  display: 'master file',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSubSection',
};
const v3_hl7PublishingSubSection_Operations: Coding = {
  code: 'PO',
  display: 'operations',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSubSection',
};
const v3_hl7PublishingSubSection_Practice: Coding = {
  code: 'PR',
  display: 'practice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSubSection',
};
const v3_hl7PublishingSubSection_Query: Coding = {
  code: 'QU',
  display: 'query',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSubSection',
};
const v3_hl7PublishingSubSection_Records: Coding = {
  code: 'RC',
  display: 'records',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSubSection',
};
const v3_hl7PublishingSubSection_Reasoning: Coding = {
  code: 'RE',
  display: 'reasoning',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSubSection',
};
const v3_hl7PublishingSubSection_Unknown: Coding = {
  code: 'UU',
  display: 'unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7PublishingSubSection',
};
/**
 *   Description:
 * Codes for HL7 publishing sub-sections (business sub-categories)
 */
export const V3Hl7PublishingSubSection = {
  /**
   * Description: Represents the HL7 V3 publishing sub-section that holds common or shared specifications within the Infrastructure Management (IM) section.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  Common: v3_hl7PublishingSubSection_Common,
  /**
   * Description: Represents the HL7 V3 publishing sub-section that holds specifications related to the management of financial information within the Administrative Management (AM) section.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  FinancialInformation: v3_hl7PublishingSubSection_FinancialInformation,
  /**
   * Description: Represents the HL7 V3 publishing sub-section that holds specifications related to the definition and control of interoperability messages within the Infrastructure Management (IM) section.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  MessageControl: v3_hl7PublishingSubSection_MessageControl,
  /**
   * Description: Represents the HL7 V3 publishing sub-section that holds specifications related to master file and registry management activities within the Infrastructure Management (IM) section.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  MasterFile: v3_hl7PublishingSubSection_MasterFile,
  /**
   * Description: Represents the HL7 V3 publishing sub-section that holds specifications related to managing clinical operations within the Health and Clinical Management (HM) section.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  Operations: v3_hl7PublishingSubSection_Operations,
  /**
   * Description: Represents the HL7 V3 publishing sub-section that holds specifications related to the management of practice settings within the Administrative Management (AM) section.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  Practice: v3_hl7PublishingSubSection_Practice,
  /**
   * Description: Represents the HL7 V3 publishing sub-section that holds specifications related to query/response activities within the Infrastructure Management (IM) section.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  Query: v3_hl7PublishingSubSection_Query,
  /**
   * Description: Represents the HL7 V3 publishing sub-section that holds specifications related to the definition and communication of records of clinical care within the Health and Clinical Management (HM) section.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  Records: v3_hl7PublishingSubSection_Records,
  /**
   * Description: Represents the HL7 V3 publishing sub-section that holds specifications related to the definition and communication of reasoning (knowledge) within the Health and Clinical Management (HM) section.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  Reasoning: v3_hl7PublishingSubSection_Reasoning,
  /**
   * Description: Represents the HL7 V3 publishing sub-section that holds specifications that are unassigned - that have not yet been assigned to one of the formal publishing sections.
   *
   *
   *                            UsageNote: V3 Specifications are published in a set of "domains", which contain interactions and related specifications for a single area of health care within which can be supported by a single, coherent set of interoperability specifications.
   *
   *                         For publishing purposes, these domains are aggregated into sub-sections of related health care areas and these sub-sections are further aggregated into three major sets.
   */
  Unknown: v3_hl7PublishingSubSection_Unknown,
};
const v3_hl7Realm_AffiliateRealms: Coding = {
  code: 'AffiliateRealms',
  display: 'Affiliate Realms',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Argentina: Coding = {
  code: 'AR',
  display: 'Argentina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Austria: Coding = {
  code: 'AT',
  display: 'Austria',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Australia: Coding = {
  code: 'AU',
  display: 'Australia',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_BindingRealms: Coding = {
  code: 'BindingRealms',
  display: 'binding realms',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Brazil: Coding = {
  code: 'BR',
  display: 'Brazil',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_UnclassifiedRealm: Coding = {
  code: 'C1',
  display: 'Unclassified Realm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Canada: Coding = {
  code: 'CA',
  display: 'Canada',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Switzerland: Coding = {
  code: 'CH',
  display: 'Switzerland',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Chile: Coding = {
  code: 'CL',
  display: 'Chile',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_China: Coding = {
  code: 'CN',
  display: 'China',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Columbia: Coding = {
  code: 'CO',
  display: 'Columbia',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_CzechRepublic: Coding = {
  code: 'CZ',
  display: 'Czech Republic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Germany: Coding = {
  code: 'DE',
  display: 'Germany',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Denmark: Coding = {
  code: 'DK',
  display: 'Denmark',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Spain: Coding = {
  code: 'ES',
  display: 'Spain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Finland: Coding = {
  code: 'FI',
  display: 'Finland',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_France: Coding = {
  code: 'FR',
  display: 'France',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_GreatBritain: Coding = {
  code: 'GB',
  display: 'Great Britain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Greece: Coding = {
  code: 'GR',
  display: 'Greece',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Croatia: Coding = {
  code: 'HR',
  display: 'Croatia',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Ireland: Coding = {
  code: 'IE',
  display: 'Ireland',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_India: Coding = {
  code: 'IN',
  display: 'India',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Italy: Coding = {
  code: 'IT',
  display: 'Italy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Japan: Coding = {
  code: 'JP',
  display: 'Japan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Korea: Coding = {
  code: 'KR',
  display: 'Korea',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Lithuania: Coding = {
  code: 'LT',
  display: 'Lithuania',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Mexico: Coding = {
  code: 'MX',
  display: 'Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_NamespaceRealms: Coding = {
  code: 'NamespaceRealms',
  display: 'namespace realms',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_TheNetherlands: Coding = {
  code: 'NL',
  display: 'The Netherlands',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_NewZealand: Coding = {
  code: 'NZ',
  display: 'New Zealand',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_RepresentativeRealm: Coding = {
  code: 'R1',
  display: 'Representative Realm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Romania: Coding = {
  code: 'RO',
  display: 'Romania',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_RussianFederation: Coding = {
  code: 'RU',
  display: 'Russian Federation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Sweden: Coding = {
  code: 'SE',
  display: 'Sweden',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Singapore: Coding = {
  code: 'SG',
  display: 'Singapore',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_SouthernAfrica: Coding = {
  code: 'SOA',
  display: 'Southern Africa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Turkey: Coding = {
  code: 'TR',
  display: 'Turkey',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Taiwan: Coding = {
  code: 'TW',
  display: 'Taiwan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_UnitedKingdom: Coding = {
  code: 'UK',
  display: 'United Kingdom',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_UnitedStatesOfAmerica: Coding = {
  code: 'US',
  display: 'United States of America',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Universal: Coding = {
  code: 'UV',
  display: 'Universal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_Uruguay: Coding = {
  code: 'UY',
  display: 'Uruguay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_ExampleRealm: Coding = {
  code: 'X1',
  display: 'Example Realm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
const v3_hl7Realm_LocalizedVersion: Coding = {
  code: 'ZZ',
  display: 'Localized Version',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7Realm',
};
/**
 *   Description:
 * Coded concepts representing Binding Realms (used for Context Binding of terminology in HL7 models)  and/or Namespace Realms (used to help ensure unique identification of HL7 artifacts). This code system is partitioned into three sections: Affiliate realms, Binding realms and Namespace realms.  All affiliate realm codes may automatically be used as both binding realms and namespace realms.  Furthermore, affiliate realms are the only realms that have authority over the creation of binding realms.  (Note that 'affiliate' includes the idea of both international affiliates and the HL7 International organization.)  All other codes must be associated with an owning affiliate realm and must appear as a specialization of _BindingRealm or _NamespaceRealm.  For affiliates whose concepts align with nations, the country codes from ISO 3166-1 2-character alpha are used for the code when possible so these codes should not be used for other realm types.  It is recommended that binding realm and namespace codes submitted by affiliates use the realm code as a prefix to avoid possible collisions with ISO codes.  However, tooling does not currently support namepace realm codes greater than 2 characters.  Open Issue:
 * The name of the concept property "owningAffiliate" should be changed to better reflect that the property value is the human readable name of the organizational entity that manages the Realm identified by the Realm Code.  Open Issue:
 * In spite of the inability of tooling to process codes longer than 2 characters, there is at least one realm codes ('SOA') that was added that is 3 characters in length.
 */
export const V3Hl7Realm = {
  /**
   * Description: Realm codes for official HL7 organizational bodies.  This includes both the HL7 International organization as well as all recognized international affiliates (past and present).  These groups have the ability to bind vocabulary and develop artifacts.  As well, they have the ability to have "ownership" over other binding realms and namespace realms via the owningAffiliate property of those other realm codes.
   */
  AffiliateRealms: v3_hl7Realm_AffiliateRealms,
  /**
   * Description: Realm code for use of Argentina
   */
  Argentina: v3_hl7Realm_Argentina,
  /**
   * Description: Realm code for use of Austria
   */
  Austria: v3_hl7Realm_Austria,
  /**
   * Description: Realm code for use of Australia
   */
  Australia: v3_hl7Realm_Australia,
  /**
   * Description: Concepts that can be used as Binding Realms when creating Binding Statements.  These codes are permitted to appear in the InfrastructureRoot.realmCode attribute.
   */
  BindingRealms: v3_hl7Realm_BindingRealms,
  /**
   * Description: Realm code for use of Brazil
   */
  Brazil: v3_hl7Realm_Brazil,
  /**
   * Description: Realm code for use of Unclassified Realm
   */
  UnclassifiedRealm: v3_hl7Realm_UnclassifiedRealm,
  /**
   * Description: Realm code for use of Canada
   */
  Canada: v3_hl7Realm_Canada,
  /**
   * Description: Realm code for use of Switzerland
   */
  Switzerland: v3_hl7Realm_Switzerland,
  /**
   * Description: Realm code for use of Chile
   */
  Chile: v3_hl7Realm_Chile,
  /**
   * Description: Realm code for use of China
   */
  China: v3_hl7Realm_China,
  /**
   * Description: Realm code for use of Localized Version
   */
  Columbia: v3_hl7Realm_Columbia,
  /**
   * Description: Realm code for use of Czech Republic
   */
  CzechRepublic: v3_hl7Realm_CzechRepublic,
  /**
   * Description: Realm code for use of Germany
   */
  Germany: v3_hl7Realm_Germany,
  /**
   * Description: Realm code for use of Denmark
   */
  Denmark: v3_hl7Realm_Denmark,
  /**
   * Description: Realm code for use of Spain
   */
  Spain: v3_hl7Realm_Spain,
  /**
   * Description: Realm code for use of Finland
   */
  Finland: v3_hl7Realm_Finland,
  /**
   * Description: Realm code for use of France
   */
  France: v3_hl7Realm_France,
  /**
   * Description: Realm code for use of Great Britain
   */
  GreatBritain: v3_hl7Realm_GreatBritain,
  /**
   * Description: Realm code for use of Greece
   */
  Greece: v3_hl7Realm_Greece,
  /**
   * Description: Realm code for use of Croatia
   */
  Croatia: v3_hl7Realm_Croatia,
  /**
   * Description: Realm code for use of Ireland
   */
  Ireland: v3_hl7Realm_Ireland,
  /**
   * Description: Realm code for use of India
   */
  India: v3_hl7Realm_India,
  /**
   * Description: Realm code for use of Italy
   */
  Italy: v3_hl7Realm_Italy,
  /**
   * Description: Realm code for use of Japan
   */
  Japan: v3_hl7Realm_Japan,
  /**
   * Description: Realm code for use of Korea
   */
  Korea: v3_hl7Realm_Korea,
  /**
   * Description: Realm code for use of Lithuania
   */
  Lithuania: v3_hl7Realm_Lithuania,
  /**
   * Description: Realm code for use of Mexico
   */
  Mexico: v3_hl7Realm_Mexico,
  /**
   * Description: Codes that can be used in the "realm" portion of HL7 v3 artifact identifiers.
   */
  NamespaceRealms: v3_hl7Realm_NamespaceRealms,
  /**
   * Description: Realm code for use of The Netherlands
   */
  TheNetherlands: v3_hl7Realm_TheNetherlands,
  /**
   * Description: Realm code for use of New Zealand
   */
  NewZealand: v3_hl7Realm_NewZealand,
  /**
   * Description: Realm code for use of Representative Realm
   */
  RepresentativeRealm: v3_hl7Realm_RepresentativeRealm,
  /**
   * Description: Realm code for use of Romania
   */
  Romania: v3_hl7Realm_Romania,
  /**
   * Description: Realm code for use of Russian Federation
   */
  RussianFederation: v3_hl7Realm_RussianFederation,
  /**
   * Description: Realm code for use of Sweden
   */
  Sweden: v3_hl7Realm_Sweden,
  /**
   * Description: Realm code for use of Localized Version
   */
  Singapore: v3_hl7Realm_Singapore,
  /**
   * Description: Realm code for use of Southern Africa
   */
  SouthernAfrica: v3_hl7Realm_SouthernAfrica,
  /**
   * Description: Realm code for use of Turkey
   */
  Turkey: v3_hl7Realm_Turkey,
  /**
   * Description: Realm code for use of Taiwan
   */
  Taiwan: v3_hl7Realm_Taiwan,
  /**
   * Description: Realm code for use of United Kingdom
   */
  UnitedKingdom: v3_hl7Realm_UnitedKingdom,
  /**
   * Description: Realm code for use of United States of America
   */
  UnitedStatesOfAmerica: v3_hl7Realm_UnitedStatesOfAmerica,
  /**
   * Description: Realm code for use of Universal realm or context, used in every instance
   */
  Universal: v3_hl7Realm_Universal,
  /**
   * Description: Realm code for use of Uruguay
   */
  Uruguay: v3_hl7Realm_Uruguay,
  /**
   * Description: Realm code for use of Example Realm
   */
  ExampleRealm: v3_hl7Realm_ExampleRealm,
  /**
   * Description: An artifact created for local use only.  This realm namespace has no owning affiliate.  Its use is uncontrolled, i.e. anyone can create artifacts using this realm namespace.  Because of this, there is a significant likelihood of artifact identifier collisions.  Implementers are encouraged to register their artifacts under an affiliate owned and controlled namespace to avoid such collision problems where possible.
   */
  LocalizedVersion: v3_hl7Realm_LocalizedVersion,
};
const v3_HL7StandardVersionCode_Ballot2008January: Coding = {
  code: 'Ballot2008Jan',
  display: 'Ballot 2008 January',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2008May: Coding = {
  code: 'Ballot2008May',
  display: 'Ballot 2008 May',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2008September: Coding = {
  code: 'Ballot2008Sep',
  display: 'Ballot 2008 September',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2009January: Coding = {
  code: 'Ballot2009Jan',
  display: 'Ballot 2009 January',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2009May: Coding = {
  code: 'Ballot2009May',
  display: 'Ballot 2009 May',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2009September: Coding = {
  code: 'Ballot2009Sep',
  display: 'Ballot 2009 September',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2010Jan: Coding = {
  code: 'Ballot2010Jan',
  display: 'Ballot 2010 Jan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2010May: Coding = {
  code: 'Ballot2010May',
  display: 'Ballot 2010 May',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2010Sep: Coding = {
  code: 'Ballot2010Sep',
  display: 'Ballot 2010 Sep',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2011Jan: Coding = {
  code: 'Ballot2011Jan',
  display: 'Ballot 2011 Jan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2011May: Coding = {
  code: 'Ballot2011May',
  display: 'Ballot 2011 May',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2011Sep: Coding = {
  code: 'Ballot2011Sep',
  display: 'Ballot 2011 Sep',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2012Jan: Coding = {
  code: 'Ballot2012Jan',
  display: 'Ballot 2012 Jan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2012May: Coding = {
  code: 'Ballot2012May',
  display: 'Ballot 2012 May',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Ballot2012Sep: Coding = {
  code: 'Ballot2012Sep',
  display: 'Ballot 2012 Sep',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_HL7VersionV3200312: Coding = {
  code: 'V3-2003-12',
  display: 'HL7 Version V3-2003-12',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_VAL2005NormativeEdition: Coding = {
  code: 'V3-2005N',
  display: '2005 Normative Edition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_VAL2006NormativeEdition: Coding = {
  code: 'V3-2006N',
  display: '2006 Normative Edition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_VAL2007NormativeEdition: Coding = {
  code: 'V3-2007N',
  display: '2007 Normative Edition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_VAL2008NormativeEdition: Coding = {
  code: 'V3-2008N',
  display: '2008 Normative Edition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_VAL2009NormativeEdition: Coding = {
  code: 'V3-2009N',
  display: '2009 Normative Edition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_VAL2010NormativeEdition: Coding = {
  code: 'V3-2010N',
  display: '2010 Normative Edition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_VAL2011NormativeEdition: Coding = {
  code: 'V3-2011N',
  display: '2011 Normative Edition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_VAL2012NormativeEdition: Coding = {
  code: 'V3-2012N',
  display: '2012 Normative Edition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
const v3_HL7StandardVersionCode_Version3PreReleaseNumber1: Coding = {
  code: 'V3PR1',
  display: 'Version3 Pre-release #1',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7StandardVersionCode',
};
/**
 *  This code system holds version codes for the Version 3 standards. Values are to be determined by HL7 and added with each new version of the HL7 Standard.
 */
export const V3HL7StandardVersionCode = {
  /**
   * The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in January 2008.
   */
  Ballot2008January: v3_HL7StandardVersionCode_Ballot2008January,
  /**
   * The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in May 2008.
   */
  Ballot2008May: v3_HL7StandardVersionCode_Ballot2008May,
  /**
   * The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in September 2008.
   */
  Ballot2008September: v3_HL7StandardVersionCode_Ballot2008September,
  /**
   * The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in January 2009.
   */
  Ballot2009January: v3_HL7StandardVersionCode_Ballot2009January,
  /**
   * The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in May 2009.
   */
  Ballot2009May: v3_HL7StandardVersionCode_Ballot2009May,
  /**
   * The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in September 2009.
   */
  Ballot2009September: v3_HL7StandardVersionCode_Ballot2009September,
  /**
   * Definition: The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in January 2010.
   */
  Ballot2010Jan: v3_HL7StandardVersionCode_Ballot2010Jan,
  /**
   * Definition: The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in May 2010.
   */
  Ballot2010May: v3_HL7StandardVersionCode_Ballot2010May,
  /**
   * Definition: The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in September 2010.
   */
  Ballot2010Sep: v3_HL7StandardVersionCode_Ballot2010Sep,
  /**
   * Definition: The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in January 2011.
   */
  Ballot2011Jan: v3_HL7StandardVersionCode_Ballot2011Jan,
  /**
   * Definition: The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in May 2011.
   */
  Ballot2011May: v3_HL7StandardVersionCode_Ballot2011May,
  /**
   * Definition: The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in September 2011.
   */
  Ballot2011Sep: v3_HL7StandardVersionCode_Ballot2011Sep,
  /**
   * Definition: The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in January 2012.
   */
  Ballot2012Jan: v3_HL7StandardVersionCode_Ballot2012Jan,
  /**
   * Definition: The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in May 2012.
   */
  Ballot2012May: v3_HL7StandardVersionCode_Ballot2012May,
  /**
   * Definition: The complete set of normative, DSTU, proposed (under ballot) and draft artifacts as published in the ballot whose ballot cycle ended in September 2012.
   */
  Ballot2012Sep: v3_HL7StandardVersionCode_Ballot2012Sep,
  /**
   * The consistent set of messaging artefacts as published or contained in repositories in December of 2003, based on the latest version of any V3 models or artefacts (RIM, Datatypes, CMETS, Common Messages, Vocabularies) as available in December of 2003. Note: This versioncode does not cover the version of the XML ITS.
   */
  HL7VersionV3200312: v3_HL7StandardVersionCode_HL7VersionV3200312,
  /**
   * Description:The consistent set of normative and DSTU messaging artifacts as published or contained in repositories in December of 2004, based on the latest version of any V3 models or artifacts (RIM, Datatypes, CMETS, Common Messages, Vocabularies) as published under the title of Normative Edition 2005. Note: This versioncode does not cover the version of the XML ITS.
   */
  VAL2005NormativeEdition: v3_HL7StandardVersionCode_VAL2005NormativeEdition,
  /**
   * Description:The consistent set of normative and DSTU messaging artifacts as published or contained in repositories in December of 2005, based on the latest version of any V3 models or artifacts (RIM, Datatypes, CMETS, Common Messages, Vocabularies) as published under the title of Normative Edition 2006. Note: This versioncode does not cover the version of the XML ITS.
   */
  VAL2006NormativeEdition: v3_HL7StandardVersionCode_VAL2006NormativeEdition,
  /**
   * Description:The consistent set of normative and DSTU messaging artifacts as published or contained in repositories in December of 2006, based on the latest version of any V3 models or artifacts (RIM, Datatypes, CMETS, Common Messages, Vocabularies) published under the title of Normative Edition 2007. Note: This versioncode does not cover the version of the XML ITS.
   */
  VAL2007NormativeEdition: v3_HL7StandardVersionCode_VAL2007NormativeEdition,
  /**
   * Definition: The consistent set of normative and DSTU messaging artifacts as published or contained in repositories in December of 2007, based on the latest version of any V3 models or artifacts (RIM, Data Types, CMETS, Common Messages, Vocabularies) published under the title of Normative Edition 2008. Note: This version code does not cover the version of the XML ITS.
   */
  VAL2008NormativeEdition: v3_HL7StandardVersionCode_VAL2008NormativeEdition,
  /**
   * Description: The consistent set of normative and DSTU messaging artifacts as published or contained in repositories in December of 2008, based on the latest version of any V3 models or artifacts (RIM, Data Types, CMETS, Common Messages, Vocabularies) published under the title of Normative Edition 2009. Note: This version code does not cover the version of the XML ITS.
   */
  VAL2009NormativeEdition: v3_HL7StandardVersionCode_VAL2009NormativeEdition,
  /**
   * Definition: The consistent set of normative and DSTU messaging artifacts as published or contained in repositories in December of 2009, based on the latest version of any V3 models or artifacts (RIM, Data Types, CMETS, Common Messages, Vocabularies) published under the title of Normative Edition 2010. Note: This version code does not cover the version of the XML ITS.
   */
  VAL2010NormativeEdition: v3_HL7StandardVersionCode_VAL2010NormativeEdition,
  /**
   * Definition: The consistent set of normative and DSTU messaging artifacts as published or contained in repositories in December of 2010, based on the latest version of any V3 models or artifacts (RIM, Data Types, CMETS, Common Messages, Vocabularies) published under the title of Normative Edition 2011. Note: This version code does not cover the version of the XML ITS.
   */
  VAL2011NormativeEdition: v3_HL7StandardVersionCode_VAL2011NormativeEdition,
  /**
   * Definition: The consistent set of normative and DSTU messaging artifacts as published or contained in repositories in December of 2011, based on the latest version of any V3 models or artifacts (RIM, Data Types, CMETS, Common Messages, Vocabularies) published under the title of Normative Edition 2012. Note: This version code does not cover the version of the XML ITS.
   */
  VAL2012NormativeEdition: v3_HL7StandardVersionCode_VAL2012NormativeEdition,
  /**
   * Includes all material published as part of the ballot package released for vote in July-August 2003.
   */
  Version3PreReleaseNumber1: v3_HL7StandardVersionCode_Version3PreReleaseNumber1,
};
const v3_HL7UpdateMode_SetUpdateMode: Coding = {
  code: '_SetUpdateMode',
  display: 'SetUpdateMode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_Add: Coding = {
  code: 'A',
  display: 'Add',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_AddOrReplace: Coding = {
  code: 'AR',
  display: 'Add or Replace',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_AddOrUpdate: Coding = {
  code: 'AU',
  display: 'Add or Update',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_Remove: Coding = {
  code: 'D',
  display: 'Remove',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_SetAdd: Coding = {
  code: 'ESA',
  display: 'Set Add',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_SetAddOrChange: Coding = {
  code: 'ESAC',
  display: 'Set Add or Change',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_SetChange: Coding = {
  code: 'ESC',
  display: 'Set Change',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_SetDelete: Coding = {
  code: 'ESD',
  display: 'Set Delete',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_Ignore: Coding = {
  code: 'I',
  display: 'Ignore',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_Key: Coding = {
  code: 'K',
  display: 'Key',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_NoChange: Coding = {
  code: 'N',
  display: 'No Change',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_Replace: Coding = {
  code: 'R',
  display: 'Replace',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_Reference: Coding = {
  code: 'REF',
  display: 'Reference',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_Unknown: Coding = {
  code: 'U',
  display: 'Unknown',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
const v3_HL7UpdateMode_Verify: Coding = {
  code: 'V',
  display: 'Verify',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HL7UpdateMode',
};
/**
 *  The possible modes of updating that occur when an attribute is received by a system that already contains values for that attribute.
 */
export const V3HL7UpdateMode = {
  /**
   * These concepts apply when the element and/or message is updating a set of items.
   */
  SetUpdateMode: v3_HL7UpdateMode_SetUpdateMode,
  /**
   * Description:The item was (or is to be) added, having not been present immediately before. (If it is already present, this may be treated as an error condition.)
   */
  Add: v3_HL7UpdateMode_Add,
  /**
   * Description:The item was (or is to be) either added or replaced.
   */
  AddOrReplace: v3_HL7UpdateMode_AddOrReplace,
  /**
   * Description: AU: If this item exists, update it with these values. If it does not exist, create it with these values. If the item is part of the collection, update each item that matches this item, and if no items match, add a new item to the collection.
   */
  AddOrUpdate: v3_HL7UpdateMode_AddOrUpdate,
  /**
   * Description:The item was (or is to be) removed (sometimes referred to as deleted). If the item is part of a collection, delete any matching items.
   */
  Remove: v3_HL7UpdateMode_Remove,
  /**
   * Add the message element to the collection of items on the receiving system that correspond to the message element.
   */
  SetAdd: v3_HL7UpdateMode_SetAdd,
  /**
   * Change the item on the receiving system that corresponds to this message element; if a matching element does not exist, add a new one created with the values in the message.
   */
  SetAddOrChange: v3_HL7UpdateMode_SetAddOrChange,
  /**
   * Change the item on the receiving system that corresponds to this message element; do not process if a matching element does not exist.
   */
  SetChange: v3_HL7UpdateMode_SetChange,
  /**
   * Delete the item on the receiving system that corresponds to this message element.
   */
  SetDelete: v3_HL7UpdateMode_SetDelete,
  /**
   * Ignore this role, it is not relevant to the update.
   */
  Ignore: v3_HL7UpdateMode_Ignore,
  /**
   * Description:This item is part of the identifying information for this object.
   */
  Key: v3_HL7UpdateMode_Key,
  /**
   * Description:There was (or is to be) no change to the item. This is primarily used when this element has not changed, but other attributes in the instance have changed.
   */
  NoChange: v3_HL7UpdateMode_NoChange,
  /**
   * Description:The item existed previously and has (or is to be) revised. (If an item does not already exist, this may be treated as an error condition.)
   */
  Replace: v3_HL7UpdateMode_Replace,
  /**
   * Description:This item provides enough information to allow a processing system to locate the full applicable record by identifying the object.
   */
  Reference: v3_HL7UpdateMode_Reference,
  /**
   * Description:Description:</b>It is not specified whether or what kind of change has occurred to the item, or whether the item is present as a reference or identifying property.
   */
  Unknown: v3_HL7UpdateMode_Unknown,
  /**
   * Verify - this message element must match a value already in the receiving systems database in order to process the message.
   */
  Verify: v3_HL7UpdateMode_Verify,
};
const v3_hl7V3Conformance_Ignored: Coding = {
  code: 'I',
  display: 'ignored',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7V3Conformance',
};
const v3_hl7V3Conformance_NotPermitted: Coding = {
  code: 'NP',
  display: 'not permitted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7V3Conformance',
};
const v3_hl7V3Conformance_Required: Coding = {
  code: 'R',
  display: 'required',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7V3Conformance',
};
const v3_hl7V3Conformance_RequiredForConsumer: Coding = {
  code: 'RC',
  display: 'required for consumer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7V3Conformance',
};
const v3_hl7V3Conformance_RequiredForInitiator: Coding = {
  code: 'RI',
  display: 'required for initiator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7V3Conformance',
};
const v3_hl7V3Conformance_Undetermined: Coding = {
  code: 'U',
  display: 'undetermined',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7V3Conformance',
};
/**
 *   Description:
 * Identifies allowed codes for HL7aTMs v3 conformance property.
 */
export const V3Hl7V3Conformance = {
  /**
   * Description: Implementers receiving this property must not raise an error if the data is received, but will not perform any useful function with the data.  This conformance level is not used in profiles or other artifacts that are specific to the "sender" or "initiator" of a communication.
   */
  Ignored: v3_hl7V3Conformance_Ignored,
  /**
   * Description: All implementers are prohibited from transmitting this content, and may raise an error if they receive it.
   */
  NotPermitted: v3_hl7V3Conformance_NotPermitted,
  /**
   * Description: All implementers must support this property.  I.e. they must be able to transmit, or to receive and usefully handle the concept.
   */
  Required: v3_hl7V3Conformance_Required,
  /**
   * Description: The element is considered "required" (i.e. must be supported) from the perspective of systems that consume  instances, but is "undetermined" for systems that generate instances.  Used only as part of specifications that define both initiator and consumer expectations.
   */
  RequiredForConsumer: v3_hl7V3Conformance_RequiredForConsumer,
  /**
   * Description: The element is considered "required" (i.e. must be supported) from the perspective of systems that generate instances, but is "undetermined" for systems that consume instances.  Used only as part of specifications that define both initiator and consumer expectations.
   */
  RequiredForInitiator: v3_hl7V3Conformance_RequiredForInitiator,
  /**
   * Description: The conformance expectations for this element have not yet been determined.
   */
  Undetermined: v3_hl7V3Conformance_Undetermined,
};
const v3_hl7VoteResolution_AffirmativeDeferred: Coding = {
  code: 'affdef',
  display: 'affirmative-deferred',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7VoteResolution',
};
const v3_hl7VoteResolution_AffirmativeIncorporated: Coding = {
  code: 'affi',
  display: 'affirmative-incorporated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7VoteResolution',
};
const v3_hl7VoteResolution_AffirmativeResolution: Coding = {
  code: 'affirmativeResolution',
  display: 'affirmative resolution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7VoteResolution',
};
const v3_hl7VoteResolution_AffirmativeRejected: Coding = {
  code: 'affr',
  display: 'affirmative-rejected',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7VoteResolution',
};
const v3_hl7VoteResolution_NegativeResolution: Coding = {
  code: 'negativeResolution',
  display: 'negative resolution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7VoteResolution',
};
const v3_hl7VoteResolution_NonSubstantiveProposed: Coding = {
  code: 'nonsubp',
  display: 'non-substantive proposed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7VoteResolution',
};
const v3_hl7VoteResolution_NonSubstantiveVoted: Coding = {
  code: 'nonsubv',
  display: 'non-substantive voted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7VoteResolution',
};
const v3_hl7VoteResolution_NotRelatedProposed: Coding = {
  code: 'notrelp',
  display: 'not related proposed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7VoteResolution',
};
const v3_hl7VoteResolution_NotRelatedVoted: Coding = {
  code: 'notrelv',
  display: 'not related voted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7VoteResolution',
};
const v3_hl7VoteResolution_PreviouslyConsidered: Coding = {
  code: 'prevcons',
  display: 'previously considered',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7VoteResolution',
};
const v3_hl7VoteResolution_Retracted: Coding = {
  code: 'retract',
  display: 'retracted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7VoteResolution',
};
const v3_hl7VoteResolution_Unresolved: Coding = {
  code: 'unresolved',
  display: 'unresolved',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7VoteResolution',
};
const v3_hl7VoteResolution_Withdrawn: Coding = {
  code: 'withdraw',
  display: 'withdrawn',
  system: 'http://terminology.hl7.org/CodeSystem/v3-hl7VoteResolution',
};
/**
 *   Description:
 * Based on concepts for resolutions from HL7 ballot spreadsheet according to HL7's Governance & Operations Manual (GOM).
 */
export const V3Hl7VoteResolution = {
  /**
   * Description: The recommended change has been deferred to consideration for a future release.
   */
  AffirmativeDeferred: v3_hl7VoteResolution_AffirmativeDeferred,
  /**
   * Description: The recommended change has been incorporated or identified issue has been answered.
   */
  AffirmativeIncorporated: v3_hl7VoteResolution_AffirmativeIncorporated,
  /**
   * Description: An abstract concept grouping resolutions that can be applied to affirmative ballot comments.
   */
  AffirmativeResolution: v3_hl7VoteResolution_AffirmativeResolution,
  /**
   * Description: The recommended change has been refused and is not expected to be incorporated.
   */
  AffirmativeRejected: v3_hl7VoteResolution_AffirmativeRejected,
  /**
   * Description: An abstract concept grouping resolutions that can be applied to negative ballot comments.
   */
  NegativeResolution: v3_hl7VoteResolution_NegativeResolution,
  /**
   * Description: Responsible group has recommended that the negative vote be considered non-substantive.  (Issue raised does not provide sufficiently convincing reason to make changes to the item under ballot, or otherwise impede its adoption.)
   */
  NonSubstantiveProposed: v3_hl7VoteResolution_NonSubstantiveProposed,
  /**
   * Description: Ballot group has voted and declared the negative vote non-substantive.
   */
  NonSubstantiveVoted: v3_hl7VoteResolution_NonSubstantiveVoted,
  /**
   * Description: Responsible group has recommended that the negative vote be considered not-related.  (Issue raised is not related to the current scope of the item under ballot, or does not prevent the item under ballot for being used for its defined intent.  Recommended changes may be considered as part of future versions.)  (Perhaps after further reading or explanation).
   */
  NotRelatedProposed: v3_hl7VoteResolution_NotRelatedProposed,
  /**
   * Description: Ballot group has voted and declared the negative vote non-related.
   */
  NotRelatedVoted: v3_hl7VoteResolution_NotRelatedVoted,
  /**
   * Description: Committee identifies that the same issue has been raised as part of a previous ballot on the same element version and was found by the ballot group to be non-substantive or not related.)
   */
  PreviouslyConsidered: v3_hl7VoteResolution_PreviouslyConsidered,
  /**
   * Description: Voter has formally withdrawn their vote or comment as having been in error.  (Perhaps after further reading or explanation).
   */
  Retracted: v3_hl7VoteResolution_Retracted,
  /**
   * Description: Vote has not yet gone through resolution.
   */
  Unresolved: v3_hl7VoteResolution_Unresolved,
  /**
   * Description: Voter has formally withdrawn their vote or comment on the basis of agreed changes or proposed future changes.
   */
  Withdrawn: v3_hl7VoteResolution_Withdrawn,
};
const v3_HtmlLinkType_Alternate: Coding = {
  code: 'alternate',
  display: 'alternate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Appendix: Coding = {
  code: 'appendix',
  display: 'appendix',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Bookmark: Coding = {
  code: 'bookmark',
  display: 'bookmark',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Chapter: Coding = {
  code: 'chapter',
  display: 'chapter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Contents: Coding = {
  code: 'contents',
  display: 'contents',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Copyright: Coding = {
  code: 'copyright',
  display: 'copyright',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Glossary: Coding = {
  code: 'glossary',
  display: 'glossary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Help: Coding = {
  code: 'help',
  display: 'help',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Index: Coding = {
  code: 'index',
  display: 'index',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Next: Coding = {
  code: 'next',
  display: 'next',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Prev: Coding = {
  code: 'prev',
  display: 'prev',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Section: Coding = {
  code: 'section',
  display: 'section',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Start: Coding = {
  code: 'start',
  display: 'start',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Stylesheet: Coding = {
  code: 'stylesheet',
  display: 'stylesheet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
const v3_HtmlLinkType_Subsection: Coding = {
  code: 'subsection',
  display: 'subsection',
  system: 'http://terminology.hl7.org/CodeSystem/v3-HtmlLinkType',
};
/**
 *  HtmlLinkType values are drawn from HTML 4.0 and describe the relationship between the current document and the anchor that is the target of the link
 */
export const V3HtmlLinkType = {
  /**
   * Designates substitute versions for the document in which the link occurs. When used together with the lang attribute, it implies a translated version of the document. When used together with the media attribute, it implies a version designed for a different medium (or media).
   */
  Alternate: v3_HtmlLinkType_Alternate,
  /**
   * Refers to a document serving as an appendix in a collection of documents.
   */
  Appendix: v3_HtmlLinkType_Appendix,
  /**
   * Refers to a bookmark. A bookmark is a link to a key entry point within an extended document. The title attribute may be used, for example, to label the bookmark. Note that several bookmarks may be defined in each document.
   */
  Bookmark: v3_HtmlLinkType_Bookmark,
  /**
   * Refers to a document serving as a chapter in a collection of documents.
   */
  Chapter: v3_HtmlLinkType_Chapter,
  /**
   * Refers to a document serving as a table of contents. Some user agents also support the synonym ToC (from "Table of Contents").
   */
  Contents: v3_HtmlLinkType_Contents,
  /**
   * Refers to a copyright statement for the current document.
   */
  Copyright: v3_HtmlLinkType_Copyright,
  /**
   * Refers to a document providing a glossary of terms that pertain to the current document.
   */
  Glossary: v3_HtmlLinkType_Glossary,
  /**
   * Refers to a document offering help (more information, links to other sources of information, etc.).
   */
  Help: v3_HtmlLinkType_Help,
  /**
   * Refers to a document providing an index for the current document.
   */
  Index: v3_HtmlLinkType_Index,
  /**
   * Refers to the next document in a linear sequence of documents. User agents may choose to preload the "next" document, to reduce the perceived load time.
   */
  Next: v3_HtmlLinkType_Next,
  /**
   * Refers to the previous document in an ordered series of documents. Some user agents also support the synonym "Previous".
   */
  Prev: v3_HtmlLinkType_Prev,
  /**
   * Refers to a document serving as a section in a collection of documents.
   */
  Section: v3_HtmlLinkType_Section,
  /**
   * Refers to the first document in a collection of documents. This link type tells search engines which document is considered by the author to be the starting point of the collection.
   */
  Start: v3_HtmlLinkType_Start,
  /**
   * Refers to an external style sheet. See the section on external style sheets for details. This is used together with the link type "Alternate" for user-selectable alternate style sheets.
   */
  Stylesheet: v3_HtmlLinkType_Stylesheet,
  /**
   * Refers to a document serving as a subsection in a collection of documents.
   */
  Subsection: v3_HtmlLinkType_Subsection,
};
const v3_IdentifierReliability_IssuedBySystem: Coding = {
  code: 'ISS',
  display: 'Issued by System',
  system: 'http://terminology.hl7.org/CodeSystem/v3-IdentifierReliability',
};
const v3_IdentifierReliability_UnverifiedBySystem: Coding = {
  code: 'UNV',
  display: 'Unverified by system',
  system: 'http://terminology.hl7.org/CodeSystem/v3-IdentifierReliability',
};
const v3_IdentifierReliability_VerifiedBySystem: Coding = {
  code: 'VRF',
  display: 'Verified by system',
  system: 'http://terminology.hl7.org/CodeSystem/v3-IdentifierReliability',
};
/**
 *  Specifies the reliability with which the identifier is known. This attribute MAY be used to assist with identifier matching algorithms.
 */
export const V3IdentifierReliability = {
  /**
   * Description: The identifier was issued by the system responsible for constructing the instance.
   */
  IssuedBySystem: v3_IdentifierReliability_IssuedBySystem,
  /**
   * Description: The identifier was provided to the system that constructed the instance, but has not been verified. e.g. a Drivers  license entered manually into a system by a user.
   */
  UnverifiedBySystem: v3_IdentifierReliability_UnverifiedBySystem,
  /**
   * Description: The identifier was not issued by the system responsible for constructing the instance, but the system that captured the id has verified the identifier with the issuing authority, or with another system that has verified the identifier.
   */
  VerifiedBySystem: v3_IdentifierReliability_VerifiedBySystem,
};
const v3_IdentifierScope_BusinessIdentifier: Coding = {
  code: 'BUSN',
  display: 'Business Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-IdentifierScope',
};
const v3_IdentifierScope_ObjectIdentifier: Coding = {
  code: 'OBJ',
  display: 'Object Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-IdentifierScope',
};
const v3_IdentifierScope_VersionIdentifier: Coding = {
  code: 'VER',
  display: 'Version Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-IdentifierScope',
};
const v3_IdentifierScope_ViewSpecificIdentifier: Coding = {
  code: 'VW',
  display: 'View Specific Identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-IdentifierScope',
};
/**
 *   Description:
 * Codes to specify the scope in which the identifier applies to the object with which it is associated, and used in the datatype property II.
 */
export const V3IdentifierScope = {
  /**
   * Description: An identifier whose scope is defined by the business practices associated with the object. In contrast to the other scope identifiers, the scope of the use of the id is not necessarily restricted to a single object, but may be reused for other objects closely associated with the object due to business practice.
   */
  BusinessIdentifier: v3_IdentifierScope_BusinessIdentifier,
  /**
   * Description: The identifier associated with a particular object. It remains consistent as the object undergoes state transitions.
   */
  ObjectIdentifier: v3_IdentifierScope_ObjectIdentifier,
  /**
   * Description: An identifier that references a particular object as it existed at a given point in time. The identifier SHALL change with each state transition on the object. I.e. The version identifier of an object prior to a 'suspend' state transition is distinct from the identifier of the object after the state transition. Each version identifier can be tied to exactly one ControlAct event which brought that version into being (though the control act may never be instantiated).
   *
   *
   *                             NOTE: Applications that do not support versioning of objects must ignore and not persist these ids to avoid confusion resulting from leaving the same identifier on an object that undergoes changes.
   */
  VersionIdentifier: v3_IdentifierScope_VersionIdentifier,
  /**
   * Description: An identifier that references a particular object as it existed at a given point in time. The identifier SHALL change with each state transition on the object.
   *
   *
   *                            Example The version identifier of an object prior to a 'suspend' state transition is distinct from the identifier of the object after the state transition. Each version identifier can be tied to exactly one ControlAct event which brought that version into being (though the control act may never be instantiated).
   *
   *
   *                             NOTE: Applications that do not support versioning of objects must ignore and not persist these ids to avoid confusion resulting from leaving the same identifier on an object that undergoes changes.
   */
  ViewSpecificIdentifier: v3_IdentifierScope_ViewSpecificIdentifier,
};
/**
 *  Sensitivity codes are not useful for interoperability outside of a policy domain because sensitivity policies are typically localized and vary drastically across policy domains even for the same information category because of differing organizational business rules, security policies, and jurisdictional requirements.  For example, an "employee" sensitivity code would make little sense for use outside of a policy domain.   "Taboo" would rarely be useful outside of a policy domain unless there are jurisdictional requirements requiring that a provider disclose sensitive information to a patient directly. Sensitivity codes may be more appropriate in a legacy system's Master Files in order to notify those who access a patient's orders and observations about the sensitivity policies that apply.  Newer systems may have a security engine that uses a sensitivity policy's criteria directly. The specializable Sensitivity Act.code may be useful in some scenarious if used in combination with a sensitivity identifier and/or Act.title.
 */
export const V3InformationSensitivityPolicy = {
  /**
   * Types of sensitivity policies that apply to Acts.  Act.confidentialityCode is defined in the RIM as "constraints around appropriate disclosure of information about this Act, regardless of mood."
   *
   *
   *                            Usage Note: ActSensitivity codes are used to bind information to an Act.confidentialityCode according to local sensitivity policy so that those confidentiality codes can then govern its handling across enterprises.  Internally to a policy domain, however, local policies guide the access control system on how end users in that policy domain are  able to use information tagged with these sensitivity values.
   */
  ActInformationSensitivityPolicy: v3_ActCode_ActInformationSensitivityPolicy,
  /**
   * Types of sensitivity policies that may apply to a sensitive attribute on an Entity.
   *
   *
   *                            Usage Note: EntitySensitivity codes are used to convey a policy that is applicable to sensitive information conveyed by an entity attribute.  May be used to bind a Role.confidentialityCode associated with an Entity per organizational policy.  Role.confidentialityCode is defined in the RIM as "an indication of the appropriate disclosure of information about this Role with respect to the playing Entity."
   */
  EntityInformationSensitivityPolicy: v3_ActCode_EntityInformationSensitivityPolicy,
  /**
   * A mandate, obligation, requirement, rule, or expectation characterizing the value or importance of a resource and may include its vulnerability. (Based on ISO7498-2:1989. Note: The vulnerability of personally identifiable sensitive information may be based on concerns that the unauthorized disclosure may result in social stigmatization or discrimination.) Description:  Types of Sensitivity policy that apply to Acts or Roles.  A sensitivity policy is adopted by an enterprise or group of enterprises (a 'policy domain') through a formal data use agreement that stipulates the value, importance, and vulnerability of information. A sensitivity code representing a sensitivity policy may be associated with criteria such as categories of information or sets of information identifiers (e.g., a value set of clinical codes or branch in a code system hierarchy).   These criteria may in turn be used for the Policy Decision Point in a Security Engine.  A sensitivity code may be used to set the confidentiality code used on information about Acts and Roles to trigger the security mechanisms required to control how security principals (i.e., a person, a machine, a software application) may act on the information (e.g., collection, access, use, or disclosure). Sensitivity codes are never assigned to the transport or business envelope containing patient specific information being exchanged outside of a policy domain as this would disclose the information intended to be protected by the policy.  When sensitive information is exchanged with others outside of a policy domain, the confidentiality code on the transport or business envelope conveys the receiver's responsibilities and indicates the how the information is to be safeguarded without unauthorized disclosure of the sensitive information.  This ensures that sensitive information is treated by receivers as the sender intends, accomplishing interoperability without point to point negotiations.
   *
   *
   *                            Usage Note: Sensitivity codes are not useful for interoperability outside of a policy domain because sensitivity policies are typically localized and vary drastically across policy domains even for the same information category because of differing organizational business rules, security policies, and jurisdictional requirements.  For example, an employee's sensitivity code would make little sense for use outside of a policy domain.   'Taboo' would rarely be useful outside of a policy domain unless there are jurisdictional requirements requiring that a provider disclose sensitive information to a patient directly.  Sensitivity codes may be more appropriate in a legacy system's Master Files in order to notify those who access a patient's orders and observations about the sensitivity policies that apply.  Newer systems may have a security engine that uses a sensitivity policy's criteria directly.  The specializable InformationSensitivityPolicy Act.code may be useful in some scenarios if used in combination with a sensitivity identifier and/or Act.title.
   */
  InformationSensitivityPolicy: v3_ActCode_InformationSensitivityPolicy,
  /**
   * Types of sensitivity policies that apply to Roles.
   *
   *
   *                            Usage Notes: RoleSensitivity codes are used to bind information to a Role.confidentialityCode per organizational policy.  Role.confidentialityCode is defined in the RIM as "an indication of the appropriate disclosure of information about this Role with respect to the playing Entity."
   */
  RoleInformationSensitivityPolicy: v3_ActCode_RoleInformationSensitivityPolicy,
  /**
   * Policy for handling information related to an adolescent, which will be afforded heightened confidentiality per applicable organizational or jurisdictional policy.  An enterprise may have a policy that requires that adolescent patient information be provided heightened confidentiality.  Information deemed sensitive typically includes health information and patient role information including patient status, demographics, next of kin, and location.
   *
   *
   *                            Usage Note: For use within an enterprise in which an adolescent is the information subject.  If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  AdolescentInformationSensitivity: v3_ActCode_AdolescentInformationSensitivity,
  /**
   * Policy for handling trade secrets such as financial information or intellectual property, which will be afforded heightened confidentiality.  Description:  Since the service class can represent knowledge structures that may be considered a trade or business secret, there is sometimes (though rarely) the need to flag those items as of business level confidentiality.
   *
   *
   *                            Usage Notes: No patient related information may ever be of this confidentiality level.   If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  BusinessInformationSensitivity: v3_ActCode_BusinessInformationSensitivity,
  /**
   * Policy for handling information related to behavioral and emotional disturbances affecting social adjustment and physical health, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  BehavioralHealthInformationSensitivity: v3_ActCode_BehavioralHealthInformationSensitivity,
  /**
   * Policy for handling information related to a celebrity (people of public interest (VIP), which will be afforded heightened confidentiality.  Celebrities are people of public interest (VIP) about whose information an enterprise may have a policy that requires heightened confidentiality.  Information deemed sensitive may include health information and patient role information including patient status, demographics, next of kin, and location.
   *
   *
   *                            Usage Note:  For use within an enterprise in which the information subject is deemed a celebrity or very important person.  If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  CelebrityInformationSensitivity: v3_ActCode_CelebrityInformationSensitivity,
  /**
   * Policy for handling information related to cognitive disability disorders and conditions caused by these disorders, which are afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   *
   *                         Examples may include dementia, traumatic brain injury, attention deficit, hearing and visual disability such as dyslexia and other disorders and related conditions which impair learning and self-sufficiency.  However, the cognitive disabilities to which this term may apply  versus other behavioral health categories varies by jurisdiction and organizational policy in part due to overlap with other behavioral health conditions. Implementers should constrain to those diagnoses applicable in the domain in which this code is used.
   */
  CognitiveDisabilityInformationSensitivity: v3_ActCode_CognitiveDisabilityInformationSensitivity,
  /**
   * Policy for handling all demographic information about an information subject, which will be afforded heightened confidentiality. Policies may govern sensitivity of information related to all demographic about an information subject, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  AllDemographicInformationSensitivity: v3_ActCode_AllDemographicInformationSensitivity,
  /**
   * Policy for handling information related to a diagnosis, health condition or health problem, which will be afforded heightened confidentiality.  Diagnostic, health condition or health problem related information may be deemed sensitive by organizational policy, and require heightened confidentiality.
   *
   *
   *                            Usage Note: For use within an enterprise that provides heightened confidentiality to  diagnostic, health condition or health problem related information deemed sensitive.   If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  DiagnosisInformationSensitivity: v3_ActCode_DiagnosisInformationSensitivity,
  /**
   * Policy for handling information related to an information subject's date of birth, which will be afforded heightened confidentiality.Policies may govern sensitivity of information related to an information subject's date of birth, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  DateOfBirthInformationSensitivity: v3_ActCode_DateOfBirthInformationSensitivity,
  /**
   * Policy for handling information related to a drug, which will be afforded heightened confidentiality. Drug information may be deemed sensitive by organizational policy, and require heightened confidentiality.
   *
   *
   *                            Usage Note: For use within an enterprise that provides heightened confidentiality to drug information deemed sensitive.   If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  DrugInformationSensitivity: v3_ActCode_DrugInformationSensitivity,
  /**
   * Policy for handling information related to developmental disability disorders and conditions caused by these disorders, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   *
   *                         A diverse group of chronic conditions that are due to mental or physical impairments impacting activities of daily living, self-care, language acuity, learning, mobility, independent living and economic self-sufficiency. Examples may include Down syndrome and  Autism spectrum. However, the developmental disabilities to which this term applies versus other behavioral health categories varies by jurisdiction and organizational policy in part due to overlap with other behavioral health conditions.  Implementers should constrain to those diagnoses applicable in the domain in which this code is used.
   */
  DevelopmentalDisabilityInformationSensitivity: v3_ActCode_DevelopmentalDisabilityInformationSensitivity,
  /**
   * Policy for handling information related to emotional disturbance disorders and conditions caused by these disorders, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   *
   *                         Typical used to characterize behavioral and mental health issues of adolescents where the disorder may be temporarily diagnosed in order to avoid the potential and unnecessary stigmatizing diagnoses of disorder long term.
   */
  EmotionalDisturbanceInformationSensitivity: v3_ActCode_EmotionalDisturbanceInformationSensitivity,
  /**
   * Policy for handling information related to an employee, which will be afforded heightened confidentiality. When a patient is an employee, an enterprise may have a policy that requires heightened confidentiality.  Information deemed sensitive typically includes health information and patient role information including patient status, demographics, next of kin, and location.
   *
   *
   *                            Usage Note: Policy for handling information related to an employee, which will be afforded heightened confidentiality.  Description:  When a patient is an employee, an enterprise may have a policy that requires heightened confidentiality.  Information deemed sensitive typically includes health information and patient role information including patient status, demographics, next of kin, and location.
   */
  EmployeeInformationSensitivity: v3_ActCode_EmployeeInformationSensitivity,
  /**
   * Policy for handling information related to an employer which is deemed classified to protect an employee who is the information subject, and which will be afforded heightened confidentiality.  Description:  Policies may govern sensitivity of information related to an employer, such as law enforcement or national security, the identity of which could impact the privacy, well-being, or safety of an information subject who is an employee.
   *
   *
   *                            Usage Notes: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  EmployerInformationSensitivity: v3_ActCode_EmployerInformationSensitivity,
  /**
   * Policy for handling alcohol or drug-abuse information, which will be afforded heightened confidentiality.  Information handling protocols based on organizational policies related to alcohol or drug-abuse information that is deemed sensitive.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  SubstanceAbuseInformationSensitivity: v3_ActCode_SubstanceAbuseInformationSensitivity,
  /**
   * Policy for handling information related to alcohol use disorders and conditions caused by these disorders, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  AlcoholUseDisorderInformationSensitivity: v3_ActCode_AlcoholUseDisorderInformationSensitivity,
  /**
   * Policy for handling genetic disease information, which will be afforded heightened confidentiality. Information handling protocols based on organizational policies related to genetic disease information that is deemed sensitive.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  GeneticDiseaseInformationSensitivity: v3_ActCode_GeneticDiseaseInformationSensitivity,
  /**
   * Policy for handling information related to an information subject's gender and sexual orientation, which will be afforded heightened confidentiality.  Policies may govern sensitivity of information related to an information subject's gender and sexual orientation, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  GenderAndSexualOrientationInformationSensitivity: v3_ActCode_GenderAndSexualOrientationInformationSensitivity,
  /**
   * Policy for handling HIV or AIDS information, which will be afforded heightened confidentiality. Information handling protocols based on organizational policies related to HIV or AIDS information that is deemed sensitive.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  HIVAIDSInformationSensitivity: v3_ActCode_HIVAIDSInformationSensitivity,
  /**
   * Policy for handling information related to an information subject's living arrangement, which will be afforded heightened confidentiality.  Policies may govern sensitivity of information related to an information subject's living arrangement, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  LivingArrangementInformationSensitivity: v3_ActCode_LivingArrangementInformationSensitivity,
  /**
   * Policy for handling information related to the location of the information subject, which will be afforded heightened confidentiality.  Description:  Policies may govern sensitivity of information related to the location of the information subject, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Notes: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  LocationInformationSensitivity: v3_ActCode_LocationInformationSensitivity,
  /**
   * Policy for handling information related to an information subject's marital status, which will be afforded heightened confidentiality. Policies may govern sensitivity of information related to an information subject's marital status, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  MaritalStatusInformationSensitivity: v3_ActCode_MaritalStatusInformationSensitivity,
  /**
   * Policy for handling information related to psychological disorders, which is afforded heightened confidentiality. Mental health information may be deemed specifically sensitive and distinct from physical health, substance use disorders, and behavioral disabilities and disorders in some jurisdictions.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  MentalHealthInformationSensitivity: v3_ActCode_MentalHealthInformationSensitivity,
  /**
   * Policy for handling information related to sexual assault or repeated, threatening sexual harassment that occurred while the patient was in the military, which is afforded heightened confidentiality.
   *
   *                         Access control concerns for military sexual trauma is based on the patient being subject to control by a higher ranking military perpetrator and/or censure by others within the military unit.  Due to the relatively unfettered access to healthcare information by higher ranking military personnel and those who have command over the patient, there is a need to sequester this information outside of the typical controls on access to military health records.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  MilitarySexualTraumaInformationSensitivity: v3_ActCode_MilitarySexualTraumaInformationSensitivity,
  /**
   * Policy for handling information related to opioid use disorders and conditions caused by these disorders, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  OpioidUseDisorderInformationSensitivity: v3_ActCode_OpioidUseDisorderInformationSensitivity,
  /**
   * Policy for specially protecting information reported by or about a patient, which is deemed sensitive within the enterprise (i.e., by default regardless of whether the patient requested that the information be deemed sensitive for another reason.) For example information reported by the patient about another person, e.g., a family member, may be deemed sensitive by default. Organizational policy may allow the sensitivity tag to be cleared on patient's request.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   *
   *                         For example, VA deems employee information sensitive by default.  Information about a patient who is being stalked or a victim of abuse or violence may be deemed sensitive by default per a provider organization's policies.
   */
  PatientDefaultInformationSensitivity: v3_ActCode_PatientDefaultInformationSensitivity,
  /**
   * Policy for handling information about a patient, which a physician or other licensed healthcare provider deems sensitive.  Once tagged by the provider, this may trigger alerts for follow up actions according to organizational policy or jurisdictional law.
   *
   *
   *                            Usage Note: For use within an enterprise that provides heightened confidentiality to certain types of information designated by a physician as sensitive. If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   *
   *                         Use cases in which this code could be used are, e.g.,  in systems that lack the ability to automatically detect sensitive information and must rely on manual tagging; a system that lacks an applicable sensitivity tag, or for ad hoc situations where criticality of the situation requires that the tagging be done immediately by the provider before coding or transcription of consult notes can be completed, e.g., upon detection of a patient with suicidal tendencies or potential for violence.
   */
  PhysicianRequestedInformationSensitivity: v3_ActCode_PhysicianRequestedInformationSensitivity,
  /**
   * Policy for specially protecting information reported by or about a patient, which the patient deems sensitive, and the patient requests that collection, access, use, or disclosure of that information be restricted.  For example, a minor patient may request that information about reproductive health not be disclosed to the patient's family or to particular providers and payers.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  PatientRequestedInformationSensitivity: v3_ActCode_PatientRequestedInformationSensitivity,
  /**
   * Policy for handling psychiatry psychiatric disorder information, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  PsychiatryDisorderInformationSensitivity: v3_ActCode_PsychiatryDisorderInformationSensitivity,
  /**
   * Policy for handling psychotherapy note information, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: In some jurisdiction, disclosure of psychotherapy notes requires patient consent.
   *
   *                         If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  PsychotherapyNoteInformationSensitivity: v3_ActCode_PsychotherapyNoteInformationSensitivity,
  /**
   * Policy for handling information related to an information subject's race, which will be afforded heightened confidentiality.  Policies may govern sensitivity of information related to an information subject's race, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  RaceInformationSensitivity: v3_ActCode_RaceInformationSensitivity,
  /**
   * Policy for handling information related to an information subject's religious affiliation, which will be afforded heightened confidentiality.  Policies may govern sensitivity of information related to an information subject's religion, the disclosure of which could impact the privacy, well-being, or safety of that subject.
   *
   *
   *                            Usage Notes: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  ReligionInformationSensitivity: v3_ActCode_ReligionInformationSensitivity,
  /**
   * Policy for handling sickle cell disease information, which is afforded heightened confidentiality.  Information handling protocols are based on organizational policies related to sickle cell disease information, which is deemed sensitive.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then the Act valued with this ActCode should be associated with an Act valued with any applicable laws from the ActPrivacyLaw code system.
   */
  SickleCellAnemiaInformationSensitivity: v3_ActCode_SickleCellAnemiaInformationSensitivity,
  /**
   * Policy for handling sexual assault, abuse, or domestic violence information, which will be afforded heightened confidentiality. Information handling protocols based on organizational policies related to sexual assault, abuse, or domestic violence information that is deemed sensitive.
   *
   *                         SDV code covers violence perpetrated by related and non-related persons. This code should be specific to physical and mental trauma caused by a related person only.  The access control concerns are keeping the patient safe from the perpetrator who may have an abusive psychological control over the patient, may be stalking the patient, or may try to manipulate care givers into allowing the perpetrator to make contact with the patient.  The definition needs to be clarified.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  SexualAssaultAbuseOrDomesticViolenceInformationSensitivity: v3_ActCode_SexualAssaultAbuseOrDomesticViolenceInformationSensitivity,
  /**
   * Policy for handling sexuality and reproductive health information, which will be afforded heightened confidentiality.  Information handling protocols based on organizational policies related to sexuality and reproductive health information that is deemed sensitive.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  SexualityAndReproductiveHealthInformationSensitivity: v3_ActCode_SexualityAndReproductiveHealthInformationSensitivity,
  /**
   * Types of sensitivity policies that apply to Acts.  Act.confidentialityCode is defined in the RIM as "constraints around appropriate disclosure of information about this Act, regardless of mood."
   *
   *
   *                            Usage Note: ActSensitivity codes are used to bind information to an Act.confidentialityCode according to local sensitivity policy so that those confidentiality codes can then govern its handling across enterprises.  Internally to a policy domain, however, local policies guide the access control system on how end users in that policy domain are able to use information tagged with these sensitivity values.
   */
  SickleCell: v3_ActCode_SickleCell,
  /**
   * Policy for handling information deemed specially protected by law or policy including substance abuse, substance use, psychiatric, mental health, behavioral health, and cognitive disorders, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  SpeciallyProtectedInformationSensitivity: v3_ActCode_SpeciallyProtectedInformationSensitivity,
  /**
   * Policy for handling information related to a provider of sensitive services, which will be afforded heightened confidentiality.  Description:  Policies may govern sensitivity of information related to providers who deliver sensitive healthcare services in order to protect the privacy, well-being, and safety of the provider and of patients receiving sensitive services.
   *
   *
   *                            Usage Notes: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  SensitiveServiceProviderInformationSensitivity: v3_ActCode_SensitiveServiceProviderInformationSensitivity,
  /**
   * Policy for handling sexually transmitted disease information, which will be afforded heightened confidentiality.
   *  Information handling protocols based on organizational policies related to sexually transmitted disease information that is deemed sensitive.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   */
  SexuallyTransmittedDiseaseInformationSensitivity: v3_ActCode_SexuallyTransmittedDiseaseInformationSensitivity,
  /**
   * Policy for handling information related to alcohol or drug use disorders and conditions caused by these disorders, which is afforded heightened confidentiality.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  SubstanceUseDisorderInformationSensitivity: v3_ActCode_SubstanceUseDisorderInformationSensitivity,
  /**
   * Policy for handling information not to be initially disclosed or discussed with patient except by a physician assigned to patient in this case. Information handling protocols based on organizational policies related to sensitive patient information that must be initially discussed with the patient by an attending physician before being disclosed to the patient.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law rather than or in addition to this more generic code.
   *
   *
   *                            Open Issue: This definition conflates a rule and a characteristic, and there may be a similar issue with ts sibling codes.
   */
  Taboo: v3_ActCode_Taboo,
  /**
   * Policy for handling information related to harm by violence, which is afforded heightened confidentiality. Harm by violence is perpetrated by an unrelated person.
   *
   *                         Access control concerns for information about mental or physical harm resulting from violence caused by an unrelated person may include manipulation of care givers or access to records that enable the perpetrator contact or locate the patient, but the perpetrator will likely not have established abusive psychological control over the patient.
   *
   *
   *                            Usage Note: If there is a jurisdictional mandate, then use the applicable ActPrivacyLaw code system, and specify the law in addition to this more generic code.
   */
  ViolenceInformationSensitivity: v3_ActCode_ViolenceInformationSensitivity,
};
const v3_IntegrityCheckAlgorithm_SecureHashAlgorithm1: Coding = {
  code: 'SHA-1',
  display: 'secure hash algorithm - 1',
  system: 'http://terminology.hl7.org/CodeSystem/v3-IntegrityCheckAlgorithm',
};
const v3_IntegrityCheckAlgorithm_SecureHashAlgorithm256: Coding = {
  code: 'SHA-256',
  display: 'secure hash algorithm - 256',
  system: 'http://terminology.hl7.org/CodeSystem/v3-IntegrityCheckAlgorithm',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3IntegrityCheckAlgorithm = {
  /**
   * This algorithm is defined in FIPS PUB 180-1: Secure Hash Standard.  As of April 17, 1995.
   */
  SecureHashAlgorithm1: v3_IntegrityCheckAlgorithm_SecureHashAlgorithm1,
  /**
   * This algorithm is defined in FIPS PUB 180-2: Secure Hash Standard.
   */
  SecureHashAlgorithm256: v3_IntegrityCheckAlgorithm_SecureHashAlgorithm256,
};
const v3_LanguageAbilityMode_ExpressedSigned: Coding = {
  code: 'ESGN',
  display: 'Expressed signed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LanguageAbilityMode',
};
const v3_LanguageAbilityMode_ExpressedSpoken: Coding = {
  code: 'ESP',
  display: 'Expressed spoken',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LanguageAbilityMode',
};
const v3_LanguageAbilityMode_ExpressedWritten: Coding = {
  code: 'EWR',
  display: 'Expressed written',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LanguageAbilityMode',
};
const v3_LanguageAbilityMode_ReceivedSigned: Coding = {
  code: 'RSGN',
  display: 'Received signed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LanguageAbilityMode',
};
const v3_LanguageAbilityMode_ReceivedSpoken: Coding = {
  code: 'RSP',
  display: 'Received spoken',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LanguageAbilityMode',
};
const v3_LanguageAbilityMode_ReceivedWritten: Coding = {
  code: 'RWR',
  display: 'Received written',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LanguageAbilityMode',
};
/**
 *  A value representing the method of expression of the language.  Example:
 * Expressed spoken, expressed written, expressed signed, received spoken, received written, received signed.  OpenIssue:
 * Description copied from Concept Domain of same name.  Must be verified.
 */
export const V3LanguageAbilityMode = {
  /**
   * Expressed signed
   */
  ExpressedSigned: v3_LanguageAbilityMode_ExpressedSigned,
  /**
   * Expressed spoken
   */
  ExpressedSpoken: v3_LanguageAbilityMode_ExpressedSpoken,
  /**
   * Expressed written
   */
  ExpressedWritten: v3_LanguageAbilityMode_ExpressedWritten,
  /**
   * Received signed
   */
  ReceivedSigned: v3_LanguageAbilityMode_ReceivedSigned,
  /**
   * Received spoken
   */
  ReceivedSpoken: v3_LanguageAbilityMode_ReceivedSpoken,
  /**
   * Received written
   */
  ReceivedWritten: v3_LanguageAbilityMode_ReceivedWritten,
};
const v3_LanguageAbilityProficiency_Excellent: Coding = {
  code: 'E',
  display: 'Excellent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LanguageAbilityProficiency',
};
const v3_LanguageAbilityProficiency_Fair: Coding = {
  code: 'F',
  display: 'Fair',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LanguageAbilityProficiency',
};
const v3_LanguageAbilityProficiency_Good: Coding = {
  code: 'G',
  display: 'Good',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LanguageAbilityProficiency',
};
const v3_LanguageAbilityProficiency_Poor: Coding = {
  code: 'P',
  display: 'Poor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LanguageAbilityProficiency',
};
/**
 *  A value representing the level of proficiency in a language.  Example:
 * Excellent, good, fair, poor.  OpenIssue:
 * Description copied from Concept Domain of same name.  Must be verified.
 */
export const V3LanguageAbilityProficiency = {
  /**
   * Excellent
   */
  Excellent: v3_LanguageAbilityProficiency_Excellent,
  /**
   * Fair
   */
  Fair: v3_LanguageAbilityProficiency_Fair,
  /**
   * Good
   */
  Good: v3_LanguageAbilityProficiency_Good,
  /**
   * Poor
   */
  Poor: v3_LanguageAbilityProficiency_Poor,
};
const v3_LivingArrangement_CommunityShelter: Coding = {
  code: 'CS',
  display: 'community shelter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement',
};
const v3_LivingArrangement_GroupHome: Coding = {
  code: 'G',
  display: 'Group Home',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement',
};
const v3_LivingArrangement_IndependentHousehold: Coding = {
  code: 'H',
  display: 'Independent Household',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement',
};
const v3_LivingArrangement_Homeless: Coding = {
  code: 'HL',
  display: 'homeless',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement',
};
const v3_LivingArrangement_Institution: Coding = {
  code: 'I',
  display: 'Institution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement',
};
const v3_LivingArrangement_Nomadic: Coding = {
  code: 'M',
  display: 'Nomadic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement',
};
const v3_LivingArrangement_NursingHome: Coding = {
  code: 'N',
  display: 'Nursing Home',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement',
};
const v3_LivingArrangement_PrivateResidence: Coding = {
  code: 'PR',
  display: 'private residence',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement',
};
const v3_LivingArrangement_RetirementCommunity: Coding = {
  code: 'R',
  display: 'Retirement Community',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement',
};
const v3_LivingArrangement_SupportedLiving: Coding = {
  code: 'SL',
  display: 'supported living',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement',
};
const v3_LivingArrangement_Transient: Coding = {
  code: 'T',
  display: 'Transient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement',
};
const v3_LivingArrangement_ExtendedCareFacility: Coding = {
  code: 'X',
  display: 'Extended care facility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LivingArrangement',
};
/**
 *  A code depicting the living arrangements of a person
 */
export const V3LivingArrangement = {
  /**
   * Definition: A group living arrangement specifically for the care of those in need of temporary and crisis housing assistance.  Examples include domestic violence shelters, shelters for displaced or homeless individuals, Salvation Army, Jesus House, etc.  Community based services may be provided.
   */
  CommunityShelter: v3_LivingArrangement_CommunityShelter,
  /**
   * Group Home
   */
  GroupHome: v3_LivingArrangement_GroupHome,
  /**
   * Independent Household
   */
  IndependentHousehold: v3_LivingArrangement_IndependentHousehold,
  /**
   * Definition: Living arrangements lacking a permanent residence.
   */
  Homeless: v3_LivingArrangement_Homeless,
  /**
   * Institution
   */
  Institution: v3_LivingArrangement_Institution,
  /**
   * Nomadic
   */
  Nomadic: v3_LivingArrangement_Nomadic,
  /**
   * Nursing Home
   */
  NursingHome: v3_LivingArrangement_NursingHome,
  /**
   * Definition:  A living arrangement within a private residence for single family.
   */
  PrivateResidence: v3_LivingArrangement_PrivateResidence,
  /**
   * Retirement Community
   */
  RetirementCommunity: v3_LivingArrangement_RetirementCommunity,
  /**
   * Definition: Assisted living in a single family residence for persons with physical, behavioral, or functional health, or socio-economic challenges.  There may or may not be on-site supervision but the housing is designed to assist the client with developing independent living skills. Community based services may be provided.
   */
  SupportedLiving: v3_LivingArrangement_SupportedLiving,
  /**
   * Transient
   */
  Transient: v3_LivingArrangement_Transient,
  /**
   * Extended care facility
   */
  ExtendedCareFacility: v3_LivingArrangement_ExtendedCareFacility,
};
const v3_LocalMarkupIgnore_All: Coding = {
  code: 'all',
  display: 'all',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LocalMarkupIgnore',
};
const v3_LocalMarkupIgnore_Markup: Coding = {
  code: 'markup',
  display: 'markup',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LocalMarkupIgnore',
};
/**
 *  Tells a receiver to ignore just the local markup tags (local_markup, local_header, local_attr) when value="markup", or to ignore the local markup tags and all contained content when value="all"
 */
export const V3LocalMarkupIgnore = {
  /**
   * all
   */
  All: v3_LocalMarkupIgnore_All,
  /**
   * markup
   */
  Markup: v3_LocalMarkupIgnore_Markup,
};
const v3_LocalRemoteControlState_Local: Coding = {
  code: 'L',
  display: 'Local',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LocalRemoteControlState',
};
const v3_LocalRemoteControlState_Remote: Coding = {
  code: 'R',
  display: 'Remote',
  system: 'http://terminology.hl7.org/CodeSystem/v3-LocalRemoteControlState',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3LocalRemoteControlState = {
  /**
   * An equipment can either work autonomously ('Local' control state).
   */
  Local: v3_LocalRemoteControlState_Local,
  /**
   * An equipment can be controlled by another system, e.g., LAS computer ('Remote' control state).
   */
  Remote: v3_LocalRemoteControlState_Remote,
};
const v3_ManagedParticipationStatus_Active: Coding = {
  code: 'active',
  display: 'active',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ManagedParticipationStatus',
};
const v3_ManagedParticipationStatus_Cancelled: Coding = {
  code: 'cancelled',
  display: 'cancelled',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ManagedParticipationStatus',
};
const v3_ManagedParticipationStatus_Completed: Coding = {
  code: 'completed',
  display: 'completed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ManagedParticipationStatus',
};
const v3_ManagedParticipationStatus_Normal: Coding = {
  code: 'normal',
  display: 'normal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ManagedParticipationStatus',
};
const v3_ManagedParticipationStatus_Nullified: Coding = {
  code: 'nullified',
  display: 'nullified',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ManagedParticipationStatus',
};
const v3_ManagedParticipationStatus_Pending: Coding = {
  code: 'pending',
  display: 'pending',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ManagedParticipationStatus',
};
/**
 *  Codes representing the defined possible states of a Managed Participation, as defined by the Managed Participation class state machine.
 */
export const V3ManagedParticipationStatus = {
  /**
   * The state representing the fact that the Participation is in progress.
   */
  Active: v3_ManagedParticipationStatus_Active,
  /**
   * The terminal state resulting from cancellation of the Participation prior to activation.
   */
  Cancelled: v3_ManagedParticipationStatus_Cancelled,
  /**
   * The terminal state representing the successful completion of the Participation.
   */
  Completed: v3_ManagedParticipationStatus_Completed,
  /**
   * The 'typical' state. Excludes "nullified" which represents the termination state of a participation instance that was created in error.
   */
  Normal: v3_ManagedParticipationStatus_Normal,
  /**
   * The state representing the termination of a Participation instance that was created in error.
   */
  Nullified: v3_ManagedParticipationStatus_Nullified,
  /**
   * The state representing that fact that the Participation has not yet become active.
   */
  Pending: v3_ManagedParticipationStatus_Pending,
};
const v3_MapRelationship_BroaderThan: Coding = {
  code: 'BT',
  display: 'Broader Than',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MapRelationship',
};
const v3_MapRelationship_Exact: Coding = {
  code: 'E',
  display: 'Exact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MapRelationship',
};
const v3_MapRelationship_NarrowerThan: Coding = {
  code: 'NT',
  display: 'Narrower Than',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MapRelationship',
};
/**
 *  The closeness or quality of the mapping between the HL7 concept (as represented by the HL7 concept identifier) and the source coding system. The values are patterned after the similar relationships used in the UMLS Metathesaurus. Because the HL7 coding sy
 */
export const V3MapRelationship = {
  /**
   * The first concept is at a more abstract level than the second concept.  For example, Hepatitis is broader than Hepatitis A, and endocrine disease is broader than Diabetes Mellitus.  Broader than is the opposite of the narrower than relationship.
   */
  BroaderThan: v3_MapRelationship_BroaderThan,
  /**
   * The two concepts have identical meaning.
   */
  Exact: v3_MapRelationship_Exact,
  /**
   * The first concept is at a more detailed level than the second concept.  For example, Pennicillin G is narrower than Pennicillin, and vellus hair is narrower than hair.  Narrower than is the opposite of broader than.
   */
  NarrowerThan: v3_MapRelationship_NarrowerThan,
};
/**
 *  * * * No description supplied * * *  Open Issue:
 * The specific meanings of these codes can vary somewhat by jurisdiction and implementation so caution should be used when determining equivalency.
 */
export const V3MaritalStatus = {
  /**
   * Marriage contract has been declared null and to not have existed
   */
  Annulled: v3_MaritalStatus_Annulled,
  /**
   * Marriage contract has been declared dissolved and inactive
   */
  Divorced: v3_MaritalStatus_Divorced,
  /**
   * Subject to an Interlocutory Decree.
   */
  Interlocutory: v3_MaritalStatus_Interlocutory,
  /**
   * Legally Separated
   */
  LegallySeparated: v3_MaritalStatus_LegallySeparated,
  /**
   * A current marriage contract is active
   */
  Married: v3_MaritalStatus_Married,
  /**
   * More than 1 current spouse
   */
  Polygamous: v3_MaritalStatus_Polygamous,
  /**
   * No marriage contract has ever been entered
   */
  NeverMarried: v3_MaritalStatus_NeverMarried,
  /**
   * Person declares that a domestic partner relationship exists.
   */
  DomesticPartner: v3_MaritalStatus_DomesticPartner,
  /**
   * Currently not in a marriage contract.
   */
  Unmarried: v3_MaritalStatus_Unmarried,
  /**
   * The spouse has died
   */
  Widowed: v3_MaritalStatus_Widowed,
};
const v3_MessageWaitingPriority_High: Coding = {
  code: 'H',
  display: 'High',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MessageWaitingPriority',
};
const v3_MessageWaitingPriority_Low: Coding = {
  code: 'L',
  display: 'Low',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MessageWaitingPriority',
};
const v3_MessageWaitingPriority_Medium: Coding = {
  code: 'M',
  display: 'Medium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-MessageWaitingPriority',
};
/**
 *  Indicates that the receiver has messages for the sender  OpenIssue:
 * Description does not make sense relative to name of coding system.  Must be reviewed and improved.
 */
export const V3MessageWaitingPriority = {
  /**
   * High priority messages are available
   */
  High: v3_MessageWaitingPriority_High,
  /**
   * Low priority messages are available
   */
  Low: v3_MessageWaitingPriority_Low,
  /**
   * Medium priority messages are available
   */
  Medium: v3_MessageWaitingPriority_Medium,
};
const v3_RoleCode_ActiveDutyMilitary: Coding = {
  code: 'ACTMIL',
  display: 'active duty military',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Military: Coding = {
  code: 'MIL',
  display: 'military',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_RetiredMilitary: Coding = {
  code: 'RETMIL',
  display: 'retired military',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Veteran: Coding = {
  code: 'VET',
  display: 'veteran',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
/**
 *   Definition:
 * A person playing the role of program eligible under a program based on military status.  Discussion:
 * This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is either "program eligible" or "subscriber" and the person's status as a member of the military meets jurisdictional or program criteria
 */
export const V3MilitaryRoleType = {
  /**
   * Description: A person playing the role of program eligible under a program based on active military status.
   *
   *
   *                            Discussion: This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is either "program eligible" or "subscriber" and the persons status as active duty military meets jurisdictional or program criteria.
   */
  ActiveDutyMilitary: v3_RoleCode_ActiveDutyMilitary,
  /**
   * Definition: A person playing the role of program eligible under a program based on military status.
   *
   *
   *                            Discussion:  This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is either "program eligible" or "subscriber" and the person's status as a member of the military meets jurisdictional or program criteria
   */
  Military: v3_RoleCode_Military,
  /**
   * Description: A person playing the role of program eligible under a program based on retired military status.
   *
   *
   *                            Discussion: This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is either "program eligible" or "subscriber" and the persons status as retired military meets jurisdictional or program criteria.
   */
  RetiredMilitary: v3_RoleCode_RetiredMilitary,
  /**
   * Description: A person playing the role of program eligible under a program based on status as a military veteran.
   *
   *
   *                            Discussion: This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is either "program eligible" or "subscriber" and the persons status as a veteran meets jurisdictional or program criteria.
   */
  Veteran: v3_RoleCode_Veteran,
};
const v3_ModifyIndicator_ModifiedSubscription: Coding = {
  code: 'M',
  display: 'Modified subscription',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ModifyIndicator',
};
const v3_ModifyIndicator_NewSubscription: Coding = {
  code: 'N',
  display: 'New subscription',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ModifyIndicator',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3ModifyIndicator = {
  /**
   * Modified subscription to a query server.
   */
  ModifiedSubscription: v3_ModifyIndicator_ModifiedSubscription,
  /**
   * New subscription to a query server.
   */
  NewSubscription: v3_ModifyIndicator_NewSubscription,
};
const v3_NullFlavor_Derived: Coding = {
  code: 'DER',
  display: 'derived',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_NullFlavor_Invalid: Coding = {
  code: 'INV',
  display: 'invalid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_NullFlavor_Masked: Coding = {
  code: 'MSK',
  display: 'masked',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_NullFlavor_NotAvailable: Coding = {
  code: 'NAVU',
  display: 'Not available',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_NullFlavor_NegativeInfinity: Coding = {
  code: 'NINF',
  display: 'negative infinity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_NullFlavor_Other: Coding = {
  code: 'OTH',
  display: 'other',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_NullFlavor_PositiveInfinity: Coding = {
  code: 'PINF',
  display: 'positive infinity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_NullFlavor_SufficientQuantity: Coding = {
  code: 'QS',
  display: 'Sufficient Quantity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_NullFlavor_Trace: Coding = {
  code: 'TRC',
  display: 'trace',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
const v3_NullFlavor_UnEncoded: Coding = {
  code: 'UNC',
  display: 'un-encoded',
  system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
};
/**
 *  A collection of codes specifying why a valid value is not present.
 */
export const V3NullFlavor = {
  /**
   * Information was sought but not found (e.g., patient was asked but didn't know)
   */
  AskedButUnknown: v3_NullFlavor_AskedButUnknown,
  /**
   * Description:An actual value may exist, but it must be derived from the provided information (usually an EXPR generic data type extension will be used to convey the derivation expressionexpression .
   */
  Derived: v3_NullFlavor_Derived,
  /**
   * Description:The value as represented in the instance is not a member of the set of permitted data values in the constrained value domain of a variable.
   */
  Invalid: v3_NullFlavor_Invalid,
  /**
   * There is information on this item available but it has not been provided by the sender due to security, privacy or other reasons. There may be an alternate mechanism for gaining access to this information.
   *
   *                         Note: using this null flavor does provide information that may be a breach of confidentiality, even though no detail data is provided.  Its primary purpose is for those circumstances where it is necessary to inform the receiver that the information does exist without providing any detail.
   */
  Masked: v3_NullFlavor_Masked,
  /**
   * Known to have no proper value (e.g., last menstrual period for a male).
   */
  NotApplicable: v3_NullFlavor_NotApplicable,
  /**
   * This information has not been sought (e.g., patient was not asked)
   */
  NotAsked: v3_NullFlavor_NotAsked,
  /**
   * Information is not available at this time but it is expected that it will be available later.
   */
  TemporarilyUnavailable: v3_NullFlavor_TemporarilyUnavailable,
  /**
   * Information is not available at this time (with no expectation regarding whether it will or will not be available in the future).
   */
  NotAvailable: v3_NullFlavor_NotAvailable,
  /**
   * Description:The value is exceptional (missing, omitted, incomplete, improper). No information as to the reason for being an exceptional value is provided. This is the most general exceptional value. It is also the default exceptional value.
   */
  NoInformation: v3_NullFlavor_NoInformation,
  /**
   * Negative infinity of numbers.
   */
  NegativeInfinity: v3_NullFlavor_NegativeInfinity,
  /**
   * Value is not present in a message.  This is only defined in messages, never in application data!  All values not present in the message must be replaced by the applicable default, or no-information (NI) as the default of all defaults.
   */
  NotPresent: v3_NullFlavor_NotPresent,
  /**
   * Description:The actual value is not a member of the set of permitted data values in the constrained value domain of a variable. (e.g., concept not provided by required code system).
   *
   *
   *                            Usage Notes: This flavor and its specializations are most commonly used with the CD datatype and its flavors.  However, it may apply to *any* datatype where the constraints of the type are tighter than can be conveyed.  For example, a PQ that is for a true measured amount whose units are not supported in UCUM, a need to convey a REAL when the type has been constrained to INT, etc.
   *
   *                         With coded datatypes, this null flavor may only be used if the vocabulary binding has a coding strength of CNE.  By definition, all local codes and original text are part of the value set if the coding strength is CWE.
   */
  Other: v3_NullFlavor_Other,
  /**
   * Positive infinity of numbers.
   */
  PositiveInfinity: v3_NullFlavor_PositiveInfinity,
  /**
   * Description:The specific quantity is not known, but is known to be non-zero and is not specified because it makes up the bulk of the material. e.g. 'Add 10mg of ingredient X, 50mg of ingredient Y, and sufficient quantity of water to 100mL.' The null flavor would be used to express the quantity of water.
   */
  SufficientQuantity: v3_NullFlavor_SufficientQuantity,
  /**
   * The content is greater than zero, but too small to be quantified.
   */
  Trace: v3_NullFlavor_Trace,
  /**
   * Description: The actual value has not yet been encoded within the approved value domain.
   *
   *
   *                            Example: Original text or a local code has been specified but translation or encoding to the approved value set has not yet occurred due to limitations of the sending system.  Original text has been captured for a PQ, but not attempt has been made to split the value and unit or to encode the unit in UCUM.
   *
   *
   *                            Usage Notes: If it is known that it is not possible to encode the concept, OTH should be used instead.  However, use of UNC does not necessarily guarantee the concept will be encodable, only that encoding has not been attempted.
   *
   *                         Data type properties such as original text and translations may be present when this null flavor is included.
   */
  UnEncoded: v3_NullFlavor_UnEncoded,
  /**
   * Description:A proper value is applicable, but not known.
   *
   *
   *                            Usage Notes: This means the actual value is not known.  If the only thing that is unknown is how to properly express the value in the necessary constraints (value set, datatype, etc.), then the OTH or UNC flavor should be used.  No properties should be included for a datatype with this property unless:
   *
   *
   *                            Those properties themselves directly translate to a semantic of "unknown".  (E.g. a local code sent as a translation that conveys 'unknown')
   *                            Those properties further qualify the nature of what is unknown.  (E.g. specifying a use code of "H" and a URL prefix of "tel:" to convey that it is the home phone number that is unknown.)
   */
  Unknown: v3_NullFlavor_Unknown,
};
/**
 *  Conveys the mandated workflow action that an information custodian, receiver, or user must perform.  Examples:
 *   encrypt  Usage Note:
 * Per OASIS XACML, an obligation is an operation specified in a policy or policy that is performed in conjunction with the enforcement of an access control decision.
 */
export const V3ObligationPolicy = {
  /**
   * Custodian system must remove any information that could result in identifying the information subject.
   */
  Anonymize: v3_ActCode_Anonymize,
  /**
   * Custodian system must make available to an information subject upon request an accounting of certain disclosures of the individualâ€™s protected health information over a period of time.  Policy may dictate that the accounting include information about the information disclosed,  the date of disclosure, the identification of the receiver, the purpose of the disclosure, the time in which the disclosing entity must provide a response and the time period for which accountings of disclosure can be requested.
   */
  AccountingOfDisclosure: v3_ActCode_AccountingOfDisclosure,
  /**
   * Custodian system must monitor systems to ensure that all users are authorized to operate on information objects.
   */
  Audit: v3_ActCode_Audit,
  /**
   * Custodian system must monitor and maintain retrievable log for each user and operation on information.
   */
  AuditTrail: v3_ActCode_AuditTrail,
  /**
   * Custodian security system must retrieve, evaluate, and comply with the information handling directions of the Confidentiality Code associated with an information target.
   */
  ComplyWithConfidentialityCode: v3_ActCode_ComplyWithConfidentialityCode,
  /**
   * Custodian security system must retrieve, evaluate, and comply with applicable information subject consent directives.
   */
  ComplyWithConsentDirective: v3_ActCode_ComplyWithConsentDirective,
  /**
   * Custodian security system must retrieve, evaluate, and comply with applicable jurisdictional privacy policies associated with the target information.
   */
  ComplyWithJurisdictionalPrivacyPolicy: v3_ActCode_ComplyWithJurisdictionalPrivacyPolicy,
  /**
   * Custodian security system must retrieve, evaluate, and comply with applicable organizational privacy policies associated with the target information.
   */
  ComplyWithOrganizationalPrivacyPolicy: v3_ActCode_ComplyWithOrganizationalPrivacyPolicy,
  /**
   * Custodian security system must retrieve, evaluate, and comply with the organizational security policies associated with the target information.
   */
  ComplyWithOrganizationalSecurityPolicy: v3_ActCode_ComplyWithOrganizationalSecurityPolicy,
  /**
   * Custodian security system must retrieve, evaluate, and comply with applicable policies associated with the target information.
   */
  ComplyWithPolicy: v3_ActCode_ComplyWithPolicy,
  /**
   * Custodian security system must declassify information assigned security labels by instantiating a new version of the classified information so as to break the binding of the classifying security label when assigning a new security label that marks the information as unclassified in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the previous assignment and binding.
   */
  DeclassifySecurityLabel: v3_ActCode_DeclassifySecurityLabel,
  /**
   * Custodian system must strip information of data that would allow the identification of the source of the information or the information subject.
   */
  Deidentify: v3_ActCode_Deidentify,
  /**
   * Custodian system must remove target information from access after use.
   */
  DeleteAfterUse: v3_ActCode_DeleteAfterUse,
  /**
   * Custodian security system must downgrade information assigned security labels by instantiating a new version of the classified information so as to break the binding of the classifying security label when assigning a new security label that marks the information as classified at a less protected level in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the previous assignment and binding.
   */
  DowngradeSecurityLabel: v3_ActCode_DowngradeSecurityLabel,
  /**
   * Custodian security system must assign and bind security labels derived from compilations of information by aggregation or disaggregation in order to classify information compiled in the information systems under its control for collection, access, use and disclosure in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the previous assignment and binding.
   */
  DeriveSecurityLabel: v3_ActCode_DeriveSecurityLabel,
  /**
   * Custodian system must render information unreadable by algorithmically transforming plaintext into ciphertext.
   *
   *
   *
   *
   *                            Usage Notes: A mathematical transposition of a file or data stream so that it cannot be deciphered at the receiving end without the proper key. Encryption is a security feature that assures that only the parties who are supposed to be participating in a videoconference or data transfer are able to do so. It can include a password, public and private keys, or a complex combination of all.  (Per Infoway.)
   */
  Encrypt: v3_ActCode_Encrypt,
  /**
   * Custodian system must render information unreadable and unusable by algorithmically transforming plaintext into ciphertext when "at rest" or in storage.
   */
  EncryptAtRest: v3_ActCode_EncryptAtRest,
  /**
   * Custodian system must render information unreadable and unusable by algorithmically transforming plaintext into ciphertext while "in transit" or being transported by any means.
   */
  EncryptInTransit: v3_ActCode_EncryptInTransit,
  /**
   * Custodian system must render information unreadable and unusable by algorithmically transforming plaintext into ciphertext while in use such that operations permitted on the target information are limited by the license granted to the end user.
   */
  EncryptInUse: v3_ActCode_EncryptInUse,
  /**
   * Custodian system must require human review and approval for permission requested.
   */
  HumanApproval: v3_ActCode_HumanApproval,
  /**
   * Custodian security system must assign and bind security labels in order to classify information created in the information systems under its control for collection, access, use and disclosure in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the assignment and binding.
   *
   *
   *                            Usage Note: In security systems, security policy label assignments do not change, they may supersede prior assignments, and such reassignments are always tracked for auditing and other purposes.
   */
  AssignSecurityLabel: v3_ActCode_AssignSecurityLabel,
  /**
   * Custodian system must render information unreadable and unusable by algorithmically transforming plaintext into ciphertext.  User may be provided a key to decrypt per license or "shared secret".
   */
  Mask: v3_ActCode_Mask,
  /**
   * Custodian must limit access and disclosure to the minimum information required to support an authorized user's purpose of use.
   *
   *
   *                            Usage Note: Limiting the information available for access and disclosure to that an authorized user or receiver "needs to know" in order to perform permitted workflow or purpose of use.
   */
  MinimumNecessary: v3_ActCode_MinimumNecessary,
  /**
   * Conveys the mandated workflow action that an information custodian, receiver, or user must perform.
   *
   *
   *                            Usage Notes: Per ISO 22600-2, ObligationPolicy instances 'are event-triggered and define actions to be performed by manager agent'. Per HL7 Composite Security and Privacy Domain Analysis Model:  This value set refers to the action required to receive the permission specified in the privacy rule. Per OASIS XACML, an obligation is an operation specified in a policy or policy that is performed in conjunction with the enforcement of an access control decision.
   */
  ObligationPolicy: v3_ActCode_ObligationPolicy,
  /**
   * Custodian security system must persist the binding of security labels to classify information received or imported by information systems under its control for collection, access, use and disclosure in accordance with applicable jurisdictional privacy policies associated with the target information.  The system must retain an immutable record of the assignment and binding.
   */
  PersistSecurityLabel: v3_ActCode_PersistSecurityLabel,
  /**
   * Custodian must create and/or maintain human readable security label tags as required by policy.
   *
   *                         Map:  Aligns with ISO 22600-3 Section A.3.4.3 description of privacy mark:  "If present, the privacy-mark is not used for access control. The content of the privacy-mark may be defined by the security policy in force (identified by the security-policy-identifier) which may define a list of values to be used. Alternately, the value may be determined by the originator of the security-label."
   */
  PrivacyMark: v3_ActCode_PrivacyMark,
  /**
   * Custodian system must strip information of data that would allow the identification of the source of the information or the information subject.  Custodian may retain a key to relink data necessary to reidentify the information subject.
   */
  Pseudonymize: v3_ActCode_Pseudonymize,
  /**
   * Custodian system must remove information, which is not authorized to be access, used, or disclosed from records made available to otherwise authorized users.
   */
  Redact: v3_ActCode_Redact,
  /**
   * Custodian security system must declassify information assigned security labels by instantiating a new version of the classified information so as to break the binding of the classifying security label when assigning a new security label that marks the information as classified at a more protected level  in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the previous assignment and binding.
   */
  UpgradeSecurityLabel: v3_ActCode_UpgradeSecurityLabel,
};
const v3_ObservationCategory_Exam: Coding = {
  code: 'exam',
  display: 'Exam',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationCategory',
};
const v3_ObservationCategory_Imaging: Coding = {
  code: 'imaging',
  display: 'Imaging',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationCategory',
};
const v3_ObservationCategory_Laboratory: Coding = {
  code: 'laboratory',
  display: 'Laboratory',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationCategory',
};
const v3_ObservationCategory_Procedure: Coding = {
  code: 'procedure',
  display: 'Procedure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationCategory',
};
const v3_ObservationCategory_SocialHistory: Coding = {
  code: 'social-history',
  display: 'Social History',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationCategory',
};
const v3_ObservationCategory_Survey: Coding = {
  code: 'survey',
  display: 'Survey',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationCategory',
};
const v3_ObservationCategory_Therapy: Coding = {
  code: 'therapy',
  display: 'Therapy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationCategory',
};
const v3_ObservationCategory_VitalSigns: Coding = {
  code: 'vital-signs',
  display: 'Vital Signs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationCategory',
};
/**
 *  High level observation categories for the general type of observation being made. URL: http://hl7-fhir.github.io/valueset-observation-category.html This is an inline code system http://hl7.org/fhir/observation-category.
 */
export const V3ObservationCategory = {
  /**
   * Observations generated by physical exam findings including direct observations made by a clinician and use of simple instruments and the result of simple maneuvers performed directly on the patient's body.
   */
  Exam: v3_ObservationCategory_Exam,
  /**
   * Observations generated by imaging. The scope includes observations, plain x-ray, ultrasound, CT, MRI, angiography, echocardiography, nuclear medicine.
   */
  Imaging: v3_ObservationCategory_Imaging,
  /**
   * The results of observations generated by laboratories. Laboratory results are typically generated by laboratories providing analytic services in areas such as chemistry, hematology, serology, histology, cytology, anatomic pathology, microbiology, and/or virology. These observations are based on analysis of specimens obtained from the patient and submitted to the laboratory.
   */
  Laboratory: v3_ObservationCategory_Laboratory,
  /**
   * Observations generated by other procedures. This category includes observations resulting from interventional and non-interventional procedures excluding lab and imaging (e.g. cardiology catheterization, endoscopy, electrodiagnostics, etc.). Procedure results are typically generated by a clinician to provide more granular information about component observations made during a procedure, such as where a gastroenterologist reports the size of a polyp observed during a colonoscopy.
   */
  Procedure: v3_ObservationCategory_Procedure,
  /**
   * The Social History Observations define the patient's occupational, personal (e.g. lifestyle), social, and environmental history and health risk factors, as well as administrative data such as marital status, race, ethnicity and religious affiliation.
   */
  SocialHistory: v3_ObservationCategory_SocialHistory,
  /**
   * Assessment tool/survey instrument observations (e.g. Apgar Scores, Montreal Cognitive Assessment (MoCA))
   */
  Survey: v3_ObservationCategory_Survey,
  /**
   * Observations generated by non-interventional treatment protocols (e.g. occupational, physical, radiation, nutritional and medication therapy)
   */
  Therapy: v3_ObservationCategory_Therapy,
  /**
   * Clinical observations measure the body's basic functions such as such as blood pressure, heart rate, respiratory rate, height, weight, body mass index, head circumference, pulse oximetry, temperature, and body surface area.
   */
  VitalSigns: v3_ObservationCategory_VitalSigns,
};
/**
 * One or more codes providing a rough qualitative interpretation of the observation, such as "normal" / "abnormal", "low" / "high", "better" / "worse", "resistant" /  "susceptible", "expected" / "not expected". The value set is intended to be for ANY use where coded representation of an interpretation is needed.
 */
export const V3ObservationInterpretation = {
  /**
   * Codes that specify interpretation of genetic analysis, such as "positive", "negative", "carrier", "responsive", etc.
   */
  GeneticObservationInterpretation: v3_ObservationInterpretation_GeneticObservationInterpretation,
  /**
   * Interpretations of change of quantity and/or severity. At most one of B or W and one of U or D allowed.
   */
  ObservationInterpretationChange: v3_ObservationInterpretation_ObservationInterpretationChange,
  /**
   * Technical exceptions resulting in the inability to provide an interpretation. At most one allowed. Does not imply normality or severity.
   */
  ObservationInterpretationExceptions: v3_ObservationInterpretation_ObservationInterpretationExceptions,
  /**
   * Interpretation of normality or degree of abnormality (including critical or "alert" level). Concepts in this category are mutually exclusive, i.e., at most one is allowed.
   */
  ObservationInterpretationNormality: v3_ObservationInterpretation_ObservationInterpretationNormality,
  /**
   * Interpretations of anti-microbial susceptibility testing results (microbiology). At most one allowed.
   */
  ObservationInterpretationSusceptibility: v3_ObservationInterpretation_ObservationInterpretationSusceptibility,
  /**
   * The result is below the minimum detection limit (the test procedure or equipment is the limiting factor).
   *
   *                         Synonyms: Below analytical limit, low off scale.
   */
  OffScaleLow: v3_ObservationInterpretation_OffScaleLow,
  /**
   * The result is above the maximum quantifiable limit (the test procedure or equipment is the limiting factor).
   *
   *                         Synonyms: Above analytical limit, high off scale.
   */
  OffScaleHigh: v3_ObservationInterpretation_OffScaleHigh,
  /**
   * The result or observation value is outside the reference range or expected norm (as defined for the respective test procedure).
   *
   *                         [Note: Typically applies to non-numeric results.]
   */
  Abnormal: v3_ObservationInterpretation_Abnormal,
  /**
   * The result or observation value is outside a reference range or expected norm at a level at which immediate action should be considered for patient safety (as defined for the respective test procedure).
   *
   *                         [Note: Typically applies to non-numeric results.  Analogous to critical/panic limits for numeric results.]
   */
  CriticalAbnormal: v3_ObservationInterpretation_CriticalAbnormal,
  /**
   * The current result or observation value has improved compared to the previous result or observation value (the change is significant as defined in the respective test procedure).
   *
   *                         [Note: This can be applied to quantitative or qualitative observations.]
   */
  Better: v3_ObservationInterpretation_Better,
  /**
   * The patient is considered as carrier based on the testing results. A carrier is an individual who carries an altered form of a gene which can lead to having a child or offspring in future generations with a genetic disorder.
   */
  Carrier: v3_ObservationInterpretation_Carrier,
  /**
   * The current result has decreased from the previous result for a quantitative observation (the change is significant as defined in the respective test procedure).
   */
  SignificantChangeDown: v3_ObservationInterpretation_SignificantChangeDown,
  /**
   * The measurement of the specified component / analyte, organism or clinical sign above the limit of detection of the performed test or procedure.
   */
  Detected: v3_ObservationInterpretation_Detected,
  /**
   * The test or procedure was successfully performed, but the results are borderline and can neither be declared positive / negative nor detected / not detected according to the current established criteria.
   */
  Equivocal: v3_ObservationInterpretation_Equivocal,
  /**
   * The observation/test result is interpreted as being outside the inclusion range for a particular protocol within which the result is being reported.
   *
   *                         Example: A positive result on a Hepatitis screening test.
   *                            Open Issue: EX, HX, LX: These three concepts do not seem to meet a clear need in the vocabulary, and their use in observation interpretation appears likely to be covered by other existing concepts (e.g., A, H, L).  The only apparent significant difference is their reference to use in protocols for exclusion of study subjects.
   * These concepts/codes were proposed by RCRIM for use in the CTLaboratory message.  They were submitted and approved in the November 2005 Harmonization cycle in proposal "030103C_VOCAB_RCRIM_l_quade_RCRIM Obs Interp_20051028154455".  However, this proposal was not fully implemented in the vocabulary.  The proposal recommended creation of the x_ClinicalResearchExclusion domain in ObservationInterpretation with a value set including those three concepts/codes, but there is no subdomain of that name or equivalent with a binding to either of the value sets that contain these concepts/codes.
   * Members of the OO WG have recently attempted to contact members of RCRIM regarding these concepts, both by email and at the recent WGM in Atlanta, without response.  It is felt by OO that the best course of action to take at this time is to add this comprehensive Open Issue rather than deprecate these three concepts at this time, until further discussion is held.
   */
  OutsideThreshold: v3_ObservationInterpretation_OutsideThreshold,
  /**
   * This result has been evaluated in light of known contraindicators.  Once those contraindicators have been taken into account the result is determined to be "Expected"  (e.g., presence of drugs in a patient that is taking prescription medication for pain management).
   */
  Expected: v3_ObservationInterpretation_Expected,
  /**
   * The result for a quantitative observation is above the upper limit of the reference range (as defined for the respective test procedure).
   *
   *                         Synonym: Above high normal
   */
  High: v3_ObservationInterpretation_High,
  /**
   * The result for a quantitative observation is above a reference level at which immediate action should be considered for patient safety (as defined for the respective test procedure).
   *
   *                         Synonym: Above upper panic limits.
   */
  CriticalHigh: v3_ObservationInterpretation_CriticalHigh,
  /**
   * A test result that is significantly higher than the reference (normal) or therapeutic interval, but has not reached the critically high value and might need special attention, as defined by the laboratory or the clinician.
   */
  SignificantlyHigh: v3_ObservationInterpretation_SignificantlyHigh,
  /**
   * The observation/test result is interpreted as being outside the inclusion range for a particular protocol within which the result is being reported.
   *
   *                         Example: A positive result on a Hepatitis screening test.
   *                            Open Issue: EX, HX, LX: These three concepts do not seem to meet a clear need in the vocabulary, and their use in observation interpretation appears likely to be covered by other existing concepts (e.g., A, H, L).  The only apparent significant difference is their reference to use in protocols for exclusion of study subjects.  These concepts/codes were proposed by RCRIM for use in the CTLaboratory message.  They were submitted and approved in the November 2005 Harmonization cycle in proposal "030103C_VOCAB_RCRIM_l_quade_RCRIM Obs Interp_20051028154455". However, this proposal was not fully implemented in the vocabulary.  The proposal recommended creation of the x_ClinicalResearchExclusion domain in ObservationInterpretation with a value set including those three concepts/codes, but there is no subdomain of that name or equivalent with a binding to either of the value sets that contain these concepts/codes.  Members of the OO WG have recently attempted to contact members of RCRIM regarding these concepts, both by email and at the recent WGM in Atlanta, without response.  It is felt by OO that the best course of action to take at this time is to add this comprehensive Open Issue rather than deprecate these three concepts at this time, until further discussion is held.
   */
  AboveHighThreshold: v3_ObservationInterpretation_AboveHighThreshold,
  /**
   * Bacterial strain inhibited in vitro by a concentration of an antimicrobial agent that is associated with uncertain therapeutic effect. Reference: CLSI (http://www.clsi.org/Content/NavigationMenu/Resources/HarmonizedTerminologyDatabase/Harmonized_Terminolo.htm)
   * Projects: ISO 20776-1, ISO 20776-2
   *
   *                         [Note 1: Bacterial strains are categorized as intermediate by applying the appropriate breakpoints in a defined phenotypic test system.]
   *
   *                         [Note 2: This class of susceptibility implies that an infection due to the isolate can be appropriately treated in body sites where the drugs are physiologically concentrated or when a high dosage of drug can be used.]
   *
   *                         [Note 3: This class also indicates a "buffer zone," to prevent small, uncontrolled, technical factors from causing major discrepancies in interpretations.]
   *
   *                         [Note 4: These breakpoints can be altered due to changes in circumstances (e.g., changes in commonly used drug dosages, emergence of new resistance mechanisms).]
   */
  Intermediate: v3_ObservationInterpretation_Intermediate,
  /**
   * There is insufficient evidence that the species in question is a good target for therapy with the drug.  A categorical interpretation is not possible.
   *
   *                         [Note: A MIC with "IE" and/or a comment may be reported (without an accompanying S, I or R-categorization).]
   */
  InsufficientEvidence: v3_ObservationInterpretation_InsufficientEvidence,
  /**
   * The specified component / analyte, organism or clinical sign could neither be declared positive / negative nor detected / not detected by the performed test or procedure.
   *
   *
   *                            Usage Note: For example, if the specimen was degraded, poorly processed, or was missing the required anatomic structures, then "indeterminate" (i.e. "cannot be determined") is the appropriate response, not "equivocal".
   */
  Indeterminate: v3_ObservationInterpretation_Indeterminate,
  /**
   * The result for a quantitative observation is below the lower limit of the reference range (as defined for the respective test procedure).
   *
   *                         Synonym: Below low normal
   */
  Low: v3_ObservationInterpretation_Low,
  /**
   * The result for a quantitative observation is below a reference level at which immediate action should be considered for patient safety (as defined for the respective test procedure).
   *
   *                         Synonym: Below lower panic limits.
   */
  CriticalLow: v3_ObservationInterpretation_CriticalLow,
  /**
   * A test result that is significantly lower than the reference (normal) or therapeutic interval, but has not reached the critically low value and might need special attention, as defined by the laboratory or the clinician.
   */
  SignificantlyLow: v3_ObservationInterpretation_SignificantlyLow,
  /**
   * The numeric observation/test result is interpreted as being below the low threshold value for a particular protocol within which the result is being reported.
   *
   *                         Example: A Total White Blood Cell Count falling below a protocol-defined threshold value of 3000/mm^3
   *                            Open Issue: EX, HX, LX: These three concepts do not seem to meet a clear need in the vocabulary, and their use in observation interpretation appears likely to be covered by other existing concepts (e.g., A, H, L).  The only apparent significant difference is their reference to use in protocols for exclusion of study subjects.  These concepts/codes were proposed by RCRIM for use in the CTLaboratory message.  They were submitted and approved in the November 2005 Harmonization cycle in proposal "030103C_VOCAB_RCRIM_l_quade_RCRIM Obs Interp_20051028154455".  However, this proposal was not fully implemented in the vocabulary.  The proposal recommended creation of the x_ClinicalResearchExclusion domain in ObservationInterpretation with a value set including those three concepts/codes, but there is no subdomain of that name or equivalent with a binding to either of the value sets that contain these concepts/codes.  Members of the OO WG have recently attempted to contact members of RCRIM regarding these concepts, both by email and at the recent WGM in Atlanta, without response.  It is felt by OO that the best course of action to take at this time is to add this comprehensive Open Issue rather than deprecate these three concepts at this time, until further discussion is held.
   */
  BelowLowThreshold: v3_ObservationInterpretation_BelowLowThreshold,
  /**
   * The result or observation value is within the reference range or expected norm (as defined for the respective test procedure).
   *
   *                         [Note: Applies to numeric or non-numeric results.]
   */
  Normal: v3_ObservationInterpretation_Normal,
  /**
   * Use when not enough clinical trial data published by the Clinical and Laboratory Standards Institutes (CLSI) is available to establish the breakpoints for susceptible / intermediate and resistant.
   */
  NoCLSIDefinedBreakpoint: v3_ObservationInterpretation_NoCLSIDefinedBreakpoint,
  /**
   * The presence of the specified component / analyte, organism or clinical sign could not be determined within the limit of detection of the performed test or procedure.
   */
  NotDetected: v3_ObservationInterpretation_NotDetected,
  /**
   * An absence finding of the specified component / analyte, organism or clinical sign based on the established threshold of the performed test or procedure.
   *
   *                         [Note: Negative does not necessarily imply the complete absence of the specified item.]
   */
  Negative: v3_ObservationInterpretation_Negative,
  /**
   * An absence finding used to indicate that the specified component / analyte did not react measurably with the reagent.
   */
  NonReactive: v3_ObservationInterpretation_NonReactive,
  /**
   * A category used for isolates for which only a susceptible interpretive criterion has been designated because of the absence or rare occurrence of resistant strains. Isolates that have MICs above or zone diameters below the value indicated for the susceptible breakpoint should be reported as non-susceptible.
   *
   *                         NOTE 1: An isolate that is interpreted as non-susceptible does not necessarily mean that the isolate has a resistance mechanism. It is possible that isolates with MICs above the susceptible breakpoint that lack resistance mechanisms may be encountered within the wild-type distribution subsequent to the time the susceptible-only breakpoint is set.
   *
   *                         NOTE 2: For strains yielding results in the "nonsusceptible" category, organism identification and antimicrobial susceptibility test results should be confirmed.
   *
   *                         Synonym: decreased susceptibility.
   */
  NonSusceptible: v3_ObservationInterpretation_NonSusceptible,
  /**
   * Interpretations of the presence or absence of a component / analyte or organism in a test or of a sign in a clinical observation. In keeping with laboratory data processing practice, these concepts provide a categorical interpretation of the "meaning" of the quantitative value for the same observation.
   */
  ObservationInterpretationDetection: v3_ObservationInterpretation_ObservationInterpretationDetection,
  /**
   * Interpretation of the observed result taking into account additional information (contraindicators) about the patient's situation. Concepts in this category are mutually exclusive, i.e., at most one is allowed.
   */
  ObservationInterpretationExpectation: v3_ObservationInterpretation_ObservationInterpretationExpectation,
  /**
   * A presence finding of the specified component / analyte, organism or clinical sign based on the established threshold of the performed test or procedure.
   */
  Positive: v3_ObservationInterpretation_Positive,
  /**
   * Bacterial strain inhibited in vitro by a concentration of an antimicrobial agent that is associated with a high likelihood of therapeutic failure.
   * Reference: CLSI (http://www.clsi.org/Content/NavigationMenu/Resources/HarmonizedTerminologyDatabase/Harmonized_Terminolo.htm)
   * Projects: ISO 20776-1, ISO 20776-2
   *
   *                         [Note 1: Bacterial strains are categorized as resistant by applying the appropriate breakpoints in a defined phenotypic test system.]
   *
   *                         [Note 2: This breakpoint can be altered due to changes in circumstances (e.g., changes in commonly used drug dosages, emergence of new resistance mechanisms).]
   */
  Resistant: v3_ObservationInterpretation_Resistant,
  /**
   * Interpretations of the presence and level of reactivity of the specified component / analyte with the reagent in the performed laboratory test.
   */
  ReactivityObservationInterpretation: v3_ObservationInterpretation_ReactivityObservationInterpretation,
  /**
   * A presence finding used to indicate that the specified component / analyte reacted with the reagent above the reliably measurable limit of the performed test.
   */
  Reactive: v3_ObservationInterpretation_Reactive,
  /**
   * Bacterial strain inhibited by in vitro concentration of an antimicrobial agent that is associated with a high likelihood of therapeutic success.
   * Reference: CLSI (http://www.clsi.org/Content/NavigationMenu/Resources/HarmonizedTerminologyDatabase/Harmonized_Terminolo.htm)
   * Synonym (earlier term): Sensitive Projects: ISO 20776-1, ISO 20776-2
   *
   *                         [Note 1: Bacterial strains are categorized as susceptible by applying the appropriate breakpoints in a defined phenotypic system.]
   *
   *                         [Note 2: This breakpoint can be altered due to changes in circumstances (e.g., changes in commonly used drug dosages, emergence of new resistance mechanisms).]
   */
  Susceptible: v3_ObservationInterpretation_Susceptible,
  /**
   * A category that includes isolates with antimicrobial agent minimum inhibitory concentrations (MICs) that approach usually attainable blood and tissue levels and for which response rates may be lower than for susceptible isolates.
   *
   *                         Reference: CLSI document M44-A2 2009 "Method for antifungal disk diffusion susceptibility testing of yeasts; approved guideline - second edition" - page 2.
   */
  SusceptibleDoseDependent: v3_ObservationInterpretation_SusceptibleDoseDependent,
  /**
   * A category for isolates where the bacteria (e.g. enterococci) are not susceptible in vitro to a combination therapy (e.g., high-level aminoglycoside and cell wall active agent).  This is predictive that this combination therapy will not be effective.
   *
   *
   *                            Usage Note: Since the use of penicillin or ampicillin alone often results in treatment failure of serious enterococcal or other bacterial infections, combination therapy is usually indicated to enhance bactericidal activity. The synergy between a cell wall active agent (such as penicillin, ampicillin, or vancomycin) and an aminoglycoside (such as gentamicin, kanamycin or streptomycin) is best predicted by screening for high-level bacterial resistance to the aminoglycoside.
   *
   *
   *                            Open Issue: The print name of the code is very general and the description is very specific to a pair of classes of agents, which may lead to confusion of these concepts in the future should other synergies be found.
   */
  SynergyResistant: v3_ObservationInterpretation_SynergyResistant,
  /**
   * A category for isolates where the bacteria (e.g. enterococci) are susceptible in vitro to a combination therapy (e.g., high-level aminoglycoside and cell wall active agent).  This is predictive that this combination therapy will be effective.
   *
   *
   *                            Usage Note: Since the use of penicillin or ampicillin alone often results in treatment failure of serious enterococcal or other bacterial infections, combination therapy is usually indicated to enhance bactericidal activity. The synergy between a cell wall active agent (such as penicillin, ampicillin, or vancomycin) and an aminoglycoside (such as gentamicin, kanamycin or streptomycin) is best predicted by screening for high-level bacterial resistance to the aminoglycoside.
   *
   *
   *                            Open Issue: The print name of the code is very general and the description is very specific to a pair of classes of agents, which may lead to confusion of these concepts in the future should other synergies be found.
   */
  SynergySusceptible: v3_ObservationInterpretation_SynergySusceptible,
  /**
   * The current result has increased from the previous result for a quantitative observation (the change is significant as defined in the respective test procedure).
   */
  SignificantChangeUp: v3_ObservationInterpretation_SignificantChangeUp,
  /**
   * This result has been evaluated in light of known contraindicators.  Once those contraindicators have been taken into account the result is determined to be "Unexpected" (e.g., presence of non-prescribed drugs in a patient that is taking prescription medication for pain management).
   */
  Unexpected: v3_ObservationInterpretation_Unexpected,
  /**
   * The current result or observation value has degraded compared to the previous result or observation value (the change is significant as defined in the respective test procedure).
   *
   *                         [Note: This can be applied to quantitative or qualitative observations.]
   */
  Worse: v3_ObservationInterpretation_Worse,
  /**
   * A weighted presence finding used to indicate that the specified component / analyte reacted with the reagent, but below the reliably measurable limit of the performed test.
   */
  WeaklyReactive: v3_ObservationInterpretation_WeaklyReactive,
};
const v3_ObservationMethod_DecisionObservationMethod: Coding = {
  code: '_DecisionObservationMethod',
  display: 'DecisionObservationMethod',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_GeneticObservationMethod: Coding = {
  code: '_GeneticObservationMethod',
  display: 'GeneticObservationMethod',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ObservationMethodAggregate: Coding = {
  code: '_ObservationMethodAggregate',
  display: 'observation method aggregate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_VerificationMethod: Coding = {
  code: '_VerificationMethod',
  display: 'VerificationMethod',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ComplementFixation: Coding = {
  code: '0001',
  display: 'Complement fixation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ComputedAxialTomography: Coding = {
  code: '0002',
  display: 'Computed axial tomography',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_HLARAgarTest: Coding = {
  code: '0003',
  display: 'HLAR agar test',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MacroscopicObservation: Coding = {
  code: '0004',
  display: 'Macroscopic observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MagneticResonance: Coding = {
  code: '0005',
  display: 'Magnetic resonance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Morphometry: Coding = {
  code: '0006',
  display: 'Morphometry',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PositronEmissionTomography: Coding = {
  code: '0007',
  display: 'Positron emission tomography',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SAMHSAConfirmation: Coding = {
  code: '0008',
  display: 'SAMHSA confirmation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SAMHSAScreening: Coding = {
  code: '0009',
  display: 'SAMHSA screening',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SerumNeutralization: Coding = {
  code: '0010',
  display: 'Serum Neutralization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Titration: Coding = {
  code: '0011',
  display: 'Titration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Ultrasound: Coding = {
  code: '0012',
  display: 'Ultrasound',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_XRayCrystallography: Coding = {
  code: '0013',
  display: 'X-ray crystallography',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Agglutination: Coding = {
  code: '0014',
  display: 'Agglutination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BufferedAcidifiedPlateAgglutination: Coding = {
  code: '0015',
  display: 'Buffered acidified plate agglutination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_CardAgglutination: Coding = {
  code: '0016',
  display: 'Card agglutination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Hemagglutination: Coding = {
  code: '0017',
  display: 'Hemagglutination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_HemagglutinationInhibition: Coding = {
  code: '0018',
  display: 'Hemagglutination inhibition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_LatexAgglutination: Coding = {
  code: '0019',
  display: 'Latex agglutination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PlateAgglutination: Coding = {
  code: '0020',
  display: 'Plate agglutination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_RapidAgglutination: Coding = {
  code: '0021',
  display: 'Rapid agglutination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_RBCAgglutination: Coding = {
  code: '0022',
  display: 'RBC agglutination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_RivanolAgglutination: Coding = {
  code: '0023',
  display: 'Rivanol agglutination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_TubeAgglutination: Coding = {
  code: '0024',
  display: 'Tube agglutination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Bioassay: Coding = {
  code: '0025',
  display: 'Bioassay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AnimalInoculation: Coding = {
  code: '0026',
  display: 'Animal Inoculation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Cytotoxicity: Coding = {
  code: '0027',
  display: 'Cytotoxicity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_EmbryoInfectiveDose50: Coding = {
  code: '0028',
  display: 'Embryo infective dose 50',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_EmbryoLethalDose50: Coding = {
  code: '0029',
  display: 'Embryo lethal dose 50',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MouseIntercerebralInoculation: Coding = {
  code: '0030',
  display: 'Mouse intercerebral inoculation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BioassayQualitative: Coding = {
  code: '0031',
  display: 'Bioassay, qualitative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BioassayQuantitative: Coding = {
  code: '0032',
  display: 'Bioassay, quantitative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ChemicalMethod: Coding = {
  code: '0033',
  display: 'Chemical method',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_DifferentialLightAbsorptionChemicalTest: Coding = {
  code: '0034',
  display: 'Differential light absorption chemical test',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Dipstick: Coding = {
  code: '0035',
  display: 'Dipstick',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_DipstickColorimetricLaboratoryTest: Coding = {
  code: '0036',
  display: 'Dipstick colorimetric laboratory test',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_TestStrip: Coding = {
  code: '0037',
  display: 'Test strip',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Chromatography: Coding = {
  code: '0038',
  display: 'Chromatography',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AffinityChromatography: Coding = {
  code: '0039',
  display: 'Affinity chromatography',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_GasLiquidChromatography: Coding = {
  code: '0040',
  display: 'Gas liquid chromatography',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_HighPerformanceLiquidChromatography: Coding = {
  code: '0041',
  display: 'High performance liquid chromatography',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_LiquidChromatography: Coding = {
  code: '0042',
  display: 'Liquid Chromatography',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ProteinAAffinityChromatography: Coding = {
  code: '0043',
  display: 'Protein A affinity chromatography',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Coagulation: Coding = {
  code: '0044',
  display: 'Coagulation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_TiltTubeCoagulationTime: Coding = {
  code: '0045',
  display: 'Tilt tube coagulation time',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_TiltTubeReptilaseInducedCoagulation: Coding = {
  code: '0046',
  display: 'Tilt tube reptilase induced coagulation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AutomatedCount: Coding = {
  code: '0047',
  display: 'Automated count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ManualCellCount: Coding = {
  code: '0048',
  display: 'Manual cell count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PlateletCountReesEcker: Coding = {
  code: '0049',
  display: 'Platelet count, Rees-Ecker',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AerobicCulture: Coding = {
  code: '0050',
  display: 'Aerobic Culture',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AnaerobicCulture: Coding = {
  code: '0051',
  display: 'Anaerobic Culture',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ChickenEmbryoCulture: Coding = {
  code: '0052',
  display: 'Chicken embryo culture',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_DelayedSecondaryEnrichment: Coding = {
  code: '0053',
  display: 'Delayed secondary enrichment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MicroaerophilicCulture: Coding = {
  code: '0054',
  display: 'Microaerophilic Culture',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_QuantitativeMicrobialCultureCup: Coding = {
  code: '0055',
  display: 'Quantitative microbial culture, cup',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_QuantitativeMicrobialCultureDroplet: Coding = {
  code: '0056',
  display: 'Quantitative microbial culture, droplet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_QuantitativeMicrobialCultureFilterPaper: Coding = {
  code: '0057',
  display: 'Quantitative microbial culture, filter paper',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_QuantitativeMicrobialCulturePadCulture: Coding = {
  code: '0058',
  display: 'Quantitative microbial culture, pad culture',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_QuantitativeMicrobialCulturePourPlate: Coding = {
  code: '0059',
  display: 'Quantitative microbial culture, pour plate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_QuantitativeMicrobialCultureSurfaceStreak: Coding = {
  code: '0060',
  display: 'Quantitative microbial culture, surface streak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SomaticCellCulture: Coding = {
  code: '0061',
  display: 'Somatic Cell culture',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AgarDiffusion: Coding = {
  code: '0062',
  display: 'Agar diffusion',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AgarGelImmunodiffusion: Coding = {
  code: '0063',
  display: 'Agar Gel Immunodiffusion',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Electrophoresis: Coding = {
  code: '0064',
  display: 'Electrophoresis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AgaorseGelElectrophoresis: Coding = {
  code: '0065',
  display: 'Agaorse gel electrophoresis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ElectrophoresisCitrateAgar: Coding = {
  code: '0066',
  display: 'Electrophoresis, citrate agar',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Immunoelectrophoresis: Coding = {
  code: '0067',
  display: 'Immunoelectrophoresis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PolyacrylamideGelElectrophoresis: Coding = {
  code: '0068',
  display: 'Polyacrylamide gel electrophoresis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_StarchGelElectrophoresis: Coding = {
  code: '0069',
  display: 'Starch gel electrophoresis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ELISA: Coding = {
  code: '0070',
  display: 'ELISA',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ELISAAntigenCapture: Coding = {
  code: '0071',
  display: 'ELISA, antigen capture',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ELISAAvidinBiotinPeroxidaseComplex: Coding = {
  code: '0072',
  display: 'ELISA, avidin biotin peroxidase complex',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_KineticELISA: Coding = {
  code: '0073',
  display: 'Kinetic ELISA',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ELISAPeroxidaseAntiperoxidase: Coding = {
  code: '0074',
  display: 'ELISA, peroxidase-antiperoxidase',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_API20Strep: Coding = {
  code: '0075',
  display: 'API 20 Strep',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_API20A: Coding = {
  code: '0076',
  display: 'API 20A',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_API20CAUX: Coding = {
  code: '0077',
  display: 'API 20C AUX',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_API20E: Coding = {
  code: '0078',
  display: 'API 20E',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_API20NE: Coding = {
  code: '0079',
  display: 'API 20NE',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_API50CH: Coding = {
  code: '0080',
  display: 'API 50 CH',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_APIAnIDENT: Coding = {
  code: '0081',
  display: 'API An-IDENT',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_APICoryne: Coding = {
  code: '0082',
  display: 'API Coryne',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_APIRapid20E: Coding = {
  code: '0083',
  display: 'API Rapid 20E',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_APIStaph: Coding = {
  code: '0084',
  display: 'API Staph',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_APIZYM: Coding = {
  code: '0085',
  display: 'API ZYM',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BacterialIdentification: Coding = {
  code: '0086',
  display: 'Bacterial identification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MiniVIDAS: Coding = {
  code: '0087',
  display: 'mini VIDAS',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PhageSusceptibilityTyping: Coding = {
  code: '0088',
  display: 'Phage susceptibility typing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_QuadFERMPlus: Coding = {
  code: '0089',
  display: 'Quad-FERM+',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_RAPIDECStaph: Coding = {
  code: '0090',
  display: 'RAPIDEC Staph',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Staphaurex: Coding = {
  code: '0091',
  display: 'Staphaurex',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_VIDAS: Coding = {
  code: '0092',
  display: 'VIDAS',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Vitek: Coding = {
  code: '0093',
  display: 'Vitek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_VITEK2: Coding = {
  code: '0094',
  display: 'VITEK 2',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmuneStain: Coding = {
  code: '0095',
  display: 'Immune stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmunofluorescentAntibodyDirect: Coding = {
  code: '0096',
  display: 'Immunofluorescent antibody, direct',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmunofluorescentAntibodyIndirect: Coding = {
  code: '0097',
  display: 'Immunofluorescent antibody, indirect',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmunoperoxidaseAvidinBiotinComplex: Coding = {
  code: '0098',
  display: 'Immunoperoxidase, Avidin-Biotin Complex',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmunoperoxidasePeroxidaseAntiPeroxidaseComplex: Coding = {
  code: '0099',
  display: 'Immunoperoxidase, Peroxidase anti-peroxidase complex',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmunoperoxidaseProteinAPeroxidaseComplex: Coding = {
  code: '0100',
  display: 'Immunoperoxidase, Protein A-peroxidase complex',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Immunoassay: Coding = {
  code: '0101',
  display: 'Immunoassay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmunoassayQualitativeMultipleStep: Coding = {
  code: '0102',
  display: 'Immunoassay, qualitative, multiple step',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmunoassayQualitativeSingleStep: Coding = {
  code: '0103',
  display: 'Immunoassay, qualitative, single step',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Radioimmunoassay: Coding = {
  code: '0104',
  display: 'Radioimmunoassay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmunoassaySemiQuantitativeMultipleStep: Coding = {
  code: '0105',
  display: 'Immunoassay, semi-quantitative, multiple step',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmunoassaySemiQuantitativeSingleStep: Coding = {
  code: '0106',
  display: 'Immunoassay, semi-quantitative, single step',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Microscopy: Coding = {
  code: '0107',
  display: 'Microscopy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_DarkfieldMicroscopy: Coding = {
  code: '0108',
  display: 'Darkfield microscopy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ElectronMicroscopy: Coding = {
  code: '0109',
  display: 'Electron microscopy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ElectronMicroscopyTomography: Coding = {
  code: '0110',
  display: 'Electron microscopy tomography',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ElectronMicroscopyNegativeStain: Coding = {
  code: '0111',
  display: 'Electron microscopy, negative stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ElectronMicroscopyThickSection: Coding = {
  code: '0112',
  display: 'Electron microscopy, thick section',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ElectronMicroscopyThinSection: Coding = {
  code: '0113',
  display: 'Electron microscopy, thin section',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MicroscopyLight: Coding = {
  code: '0114',
  display: 'Microscopy, Light',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PolarizingLightMicroscopy: Coding = {
  code: '0115',
  display: 'Polarizing light microscopy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ScanningElectronMicroscopy: Coding = {
  code: '0116',
  display: 'Scanning electron microscopy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_TransmissionElectronMicroscopy: Coding = {
  code: '0117',
  display: 'Transmission electron microscopy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_TransparentTapeDirectExamination: Coding = {
  code: '0118',
  display: 'Transparent tape direct examination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_VAL3SelfSustainingSequenceReplication: Coding = {
  code: '0119',
  display: '3 Self-Sustaining Sequence Replication',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BranchedChainDNA: Coding = {
  code: '0120',
  display: 'Branched Chain DNA',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_HybridizationProtectionAssay: Coding = {
  code: '0121',
  display: 'Hybridization Protection Assay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmuneBlot: Coding = {
  code: '0122',
  display: 'Immune blot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_InSituHybridization: Coding = {
  code: '0123',
  display: 'In-situ hybridization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_LigaseChainReaction: Coding = {
  code: '0124',
  display: 'Ligase Chain Reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_LigationActivatedTranscription: Coding = {
  code: '0125',
  display: 'Ligation Activated Transcription',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_NucleicAcidProbe: Coding = {
  code: '0126',
  display: 'Nucleic Acid Probe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_NucleicAcidProbe_2: Coding = {
  code: '0127',
  display: 'Nucleic acid probe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_NucleicAcidProbeWithAmplification: Coding = {
  code: '0128',
  display: 'Nucleic acid probe with amplification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_NucleicAcidProbeWithTargetAmplification: Coding = {
  code: '0129',
  display: 'Nucleic acid probe with target amplification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_NucleicAcidReverseTranscription: Coding = {
  code: '0130',
  display: 'Nucleic acid reverse transcription',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_NucleicAcidSequenceBasedAnalysis: Coding = {
  code: '0131',
  display: 'Nucleic Acid Sequence Based Analysis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PolymeraseChainReaction: Coding = {
  code: '0132',
  display: 'Polymerase chain reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_QBetaReplicaseOrProbeAmplificationCategoryMethod: Coding = {
  code: '0133',
  display: 'Q-Beta Replicase or probe amplification category method',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_RestrictionFragmentLengthPolymorphism: Coding = {
  code: '0134',
  display: 'Restriction Fragment Length Polymorphism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SouthernBlot: Coding = {
  code: '0135',
  display: 'Southern Blot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_StrandDisplacementAmplification: Coding = {
  code: '0136',
  display: 'Strand Displacement Amplification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_TranscriptionMediatedAmplification: Coding = {
  code: '0137',
  display: 'Transcription Mediated Amplification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_WesternBlot: Coding = {
  code: '0138',
  display: 'Western Blot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Flocculation: Coding = {
  code: '0139',
  display: 'Flocculation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmunePrecipitation: Coding = {
  code: '0140',
  display: 'Immune precipitation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MilkRingTest: Coding = {
  code: '0141',
  display: 'Milk ring test',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Precipitin: Coding = {
  code: '0142',
  display: 'Precipitin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AcidFastStain: Coding = {
  code: '0143',
  display: 'Acid fast stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AcidFastStainFluorochrome: Coding = {
  code: '0144',
  display: 'Acid fast stain, fluorochrome',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AcidFastStainKinyounQuoteSColdCarbolfuchsin: Coding = {
  code: '0145',
  display: "Acid fast stain, Kinyoun's cold carbolfuchsin",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AcidFastStainZiehlNeelsen: Coding = {
  code: '0146',
  display: 'Acid fast stain, Ziehl-Neelsen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AcidPhosphataseStain: Coding = {
  code: '0147',
  display: 'Acid phosphatase stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AcridineOrangeStain: Coding = {
  code: '0148',
  display: 'Acridine orange stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ActiveBrilliantOrangeKHStain: Coding = {
  code: '0149',
  display: 'Active brilliant orange KH stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AlazarinRedSStain: Coding = {
  code: '0150',
  display: 'Alazarin red S stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AlcianBlueStain: Coding = {
  code: '0151',
  display: 'Alcian blue stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AlcianBlueWithPeriodicAcidSchiffStain: Coding = {
  code: '0152',
  display: 'Alcian blue with Periodic acid Schiff stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ArgentaffinStain: Coding = {
  code: '0153',
  display: 'Argentaffin stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ArgentaffinSilverStain: Coding = {
  code: '0154',
  display: 'Argentaffin silver stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AzureEosinStain: Coding = {
  code: '0155',
  display: 'Azure-eosin stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BasicFuschinStain: Coding = {
  code: '0156',
  display: 'Basic Fuschin stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BennholdStain: Coding = {
  code: '0157',
  display: 'Bennhold stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BennholdQuoteSCongoRedStain: Coding = {
  code: '0158',
  display: "Bennhold's Congo red stain",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BielschowskyStain: Coding = {
  code: '0159',
  display: 'Bielschowsky stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BielschowskyQuoteSSilverStain: Coding = {
  code: '0160',
  display: "Bielschowsky's silver stain",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BleachStain: Coding = {
  code: '0161',
  display: 'Bleach stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BodianStain: Coding = {
  code: '0162',
  display: 'Bodian stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BrownBrennStain: Coding = {
  code: '0163',
  display: 'Brown-Brenn stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ButyrateEsteraseStain: Coding = {
  code: '0164',
  display: 'Butyrate-esterase stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_CalcofluorWhiteFluorescentStain: Coding = {
  code: '0165',
  display: 'Calcofluor white fluorescent stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_CarbolFuchsinStain: Coding = {
  code: '0166',
  display: 'Carbol-fuchsin stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_CarmineStain: Coding = {
  code: '0167',
  display: 'Carmine stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ChurukianSchenkStain: Coding = {
  code: '0168',
  display: 'Churukian-Schenk stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_CongoRedStain: Coding = {
  code: '0169',
  display: 'Congo red stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_CresylEchtVioletStain: Coding = {
  code: '0170',
  display: 'Cresyl echt violet stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_CrystalVioletStain: Coding = {
  code: '0171',
  display: 'Crystal violet stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_DeGalanthaStain: Coding = {
  code: '0172',
  display: 'De Galantha stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_DieterleSilverImpregnationStain: Coding = {
  code: '0173',
  display: 'Dieterle silver impregnation stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_FiteFarcoStain: Coding = {
  code: '0174',
  display: 'Fite-Farco stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_FontanaMassonSilverStain: Coding = {
  code: '0175',
  display: 'Fontana-Masson silver stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_FouchetStain: Coding = {
  code: '0176',
  display: 'Fouchet stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_GomoriStain: Coding = {
  code: '0177',
  display: 'Gomori stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_GomoriMethenamineSilverStain: Coding = {
  code: '0178',
  display: 'Gomori methenamine silver stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_GomoriWheatlyTrichromeStain: Coding = {
  code: '0179',
  display: 'Gomori-Wheatly trichrome stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_GridleyStain: Coding = {
  code: '0180',
  display: 'Gridley stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_GrimeliusSilverStain: Coding = {
  code: '0181',
  display: 'Grimelius silver stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_GrocottStain: Coding = {
  code: '0182',
  display: 'Grocott stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_GrocottMethenamineSilverStain: Coding = {
  code: '0183',
  display: 'Grocott methenamine silver stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_HaleQuoteSColloidalFerricOxideStain: Coding = {
  code: '0184',
  display: "Hale's colloidal ferric oxide stain",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_HaleQuoteSColloidalIronStain: Coding = {
  code: '0185',
  display: "Hale's colloidal iron stain",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_HanselStain: Coding = {
  code: '0186',
  display: 'Hansel stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_HarrisRegressiveHematoxylinAndEosinStain: Coding = {
  code: '0187',
  display: 'Harris regressive hematoxylin and eosin stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_HematoxylinAndEosinStain: Coding = {
  code: '0188',
  display: 'Hematoxylin and eosin stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_HighmanStain: Coding = {
  code: '0189',
  display: 'Highman stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_HolzerStain: Coding = {
  code: '0190',
  display: 'Holzer stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_IronHematoxylinStain: Coding = {
  code: '0191',
  display: 'Iron hematoxylin stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_JonesStain: Coding = {
  code: '0192',
  display: 'Jones stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_JonesMethenamineSilverStain: Coding = {
  code: '0193',
  display: 'Jones methenamine silver stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_KossaStain: Coding = {
  code: '0194',
  display: 'Kossa stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_LawsonVanGiesonStain: Coding = {
  code: '0195',
  display: 'Lawson-Van Gieson stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_LoefflerMethyleneBlueStain: Coding = {
  code: '0196',
  display: 'Loeffler methylene blue stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_LuxolFastBlueWithCresylVioletStain: Coding = {
  code: '0197',
  display: 'Luxol fast blue with cresyl violet stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_LuxolFastBlueWithPeriodicAcidSchiffStain: Coding = {
  code: '0198',
  display: 'Luxol fast blue with Periodic acid-Schiff stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MacNealQuoteSTetrachromeBloodStain: Coding = {
  code: '0199',
  display: "MacNeal's tetrachrome blood stain",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MalloryHeidenhainStain: Coding = {
  code: '0200',
  display: 'Mallory-Heidenhain stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MassonTrichromeStain: Coding = {
  code: '0201',
  display: 'Masson trichrome stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MayerMucicarmineStain: Coding = {
  code: '0202',
  display: 'Mayer mucicarmine stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MayersProgressiveHematoxylinAndEosinStain: Coding = {
  code: '0203',
  display: 'Mayers progressive hematoxylin and eosin stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MayGrunwaldGiemsaStain: Coding = {
  code: '0204',
  display: 'May-Grunwald Giemsa stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MethylGreenStain: Coding = {
  code: '0205',
  display: 'Methyl green stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MethylGreenPyroninStain: Coding = {
  code: '0206',
  display: 'Methyl green pyronin stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ModifiedGomoriWheatlyTrichromeStain: Coding = {
  code: '0207',
  display: 'Modified Gomori-Wheatly trichrome stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ModifiedMassonTrichromeStain: Coding = {
  code: '0208',
  display: 'Modified Masson trichrome stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ModifiedTrichromeStain: Coding = {
  code: '0209',
  display: 'Modified trichrome stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MovatPentachromeStain: Coding = {
  code: '0210',
  display: 'Movat pentachrome stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MucicarmineStain: Coding = {
  code: '0211',
  display: 'Mucicarmine stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_NeutralRedStain: Coding = {
  code: '0212',
  display: 'Neutral red stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_NightBlueStain: Coding = {
  code: '0213',
  display: 'Night blue stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_NonSpecificEsteraseStain: Coding = {
  code: '0214',
  display: 'Non-specific esterase stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_OilRedOStain: Coding = {
  code: '0215',
  display: 'Oil red-O stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_OrceinStain: Coding = {
  code: '0216',
  display: 'Orcein stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PerlsQuoteStain: Coding = {
  code: '0217',
  display: "Perls' stain",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PhosphotungsticAcidHematoxylinStain: Coding = {
  code: '0218',
  display: 'Phosphotungstic acid-hematoxylin stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PotassiumFerrocyanideStain: Coding = {
  code: '0219',
  display: 'Potassium ferrocyanide stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PrussianBlueStain: Coding = {
  code: '0220',
  display: 'Prussian blue stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PutchlerModifiedBennholdStain: Coding = {
  code: '0221',
  display: 'Putchler modified Bennhold stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_QuinacrineFluorescentStain: Coding = {
  code: '0222',
  display: 'Quinacrine fluorescent stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ReticulinStain: Coding = {
  code: '0223',
  display: 'Reticulin stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_RhodamineStain: Coding = {
  code: '0224',
  display: 'Rhodamine stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SafraninStain: Coding = {
  code: '0225',
  display: 'Safranin stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SchmorlStain: Coding = {
  code: '0226',
  display: 'Schmorl stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SeiverMungerStain: Coding = {
  code: '0227',
  display: 'Seiver-Munger stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SilverStain: Coding = {
  code: '0228',
  display: 'Silver stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SpecificEsteraseStain: Coding = {
  code: '0229',
  display: 'Specific esterase stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SteinerSilverStain: Coding = {
  code: '0230',
  display: 'Steiner silver stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SudanIIIStain: Coding = {
  code: '0231',
  display: 'Sudan III stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SudanIVIStain: Coding = {
  code: '0232',
  display: 'Sudan IVI stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SulfatedAlcianBlueStain: Coding = {
  code: '0233',
  display: 'Sulfated alcian blue stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SupravitalStain: Coding = {
  code: '0234',
  display: 'Supravital stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ThioflavineSStain: Coding = {
  code: '0235',
  display: 'Thioflavine-S stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ThreeMicronGiemsaStain: Coding = {
  code: '0236',
  display: 'Three micron Giemsa stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_VassarCullingStain: Coding = {
  code: '0237',
  display: 'Vassar-Culling stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_VitalStain: Coding = {
  code: '0238',
  display: 'Vital Stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_VonKossaStain: Coding = {
  code: '0239',
  display: 'von Kossa stain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AntibioticSensitivityDisk: Coding = {
  code: '0240',
  display: 'Antibiotic sensitivity, disk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BACTECSusceptibilityTest: Coding = {
  code: '0241',
  display: 'BACTEC susceptibility test',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_DiskDilution: Coding = {
  code: '0242',
  display: 'Disk dilution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MinimumBactericidalConcentrationTestMacrodilution: Coding = {
  code: '0243',
  display: 'Minimum bactericidal concentration test, macrodilution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MinimumBactericidalConcentrationTestMicrodilution: Coding = {
  code: '0244',
  display: 'Minimum bactericidal concentration test, microdilution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MinimumInhibitoryConcentrationMacrodilution: Coding = {
  code: '0245',
  display: 'Minimum Inhibitory Concentration, macrodilution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MinimumInhibitoryConcentrationMicrodilution: Coding = {
  code: '0246',
  display: 'Minimum Inhibitory Concentration, microdilution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Turbidometric: Coding = {
  code: '0247',
  display: 'Turbidometric',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Refractometric: Coding = {
  code: '0248',
  display: 'Refractometric',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ThinLayerChromatographyTLC: Coding = {
  code: '0249',
  display: 'Thin layer chromatography (TLC)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_EMIT: Coding = {
  code: '0250',
  display: 'EMIT',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_FlowCytometryFC: Coding = {
  code: '0251',
  display: 'Flow cytometry (FC)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_RadialImmunodiffusionRID: Coding = {
  code: '0252',
  display: 'Radial immunodiffusion (RID)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_FluorescencePolarizationImmunoassayFPIA: Coding = {
  code: '0253',
  display: 'Fluorescence polarization immunoassay (FPIA)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmunofixationElectrophoresisIFE: Coding = {
  code: '0254',
  display: 'Immunofixation electrophoresis (IFE)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_EquilibriumDialysis: Coding = {
  code: '0255',
  display: 'Equilibrium dialysis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_KleihauerBetkeAcidElution: Coding = {
  code: '0256',
  display: 'Kleihauer-Betke acid elution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AntiComplementImmunofluorescenceACIF: Coding = {
  code: '0257',
  display: 'Anti-complement immunofluorescence (ACIF)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_GCMS: Coding = {
  code: '0258',
  display: 'GC/MS',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Nephelometry: Coding = {
  code: '0259',
  display: 'Nephelometry',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_IgEImmunoassayAntibody: Coding = {
  code: '0260',
  display: 'IgE immunoassay antibody',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_LymphocyteMicrocytotoxicityAssay: Coding = {
  code: '0261',
  display: 'Lymphocyte Microcytotoxicity Assay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Spectrophotometry: Coding = {
  code: '0262',
  display: 'Spectrophotometry',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AtomicAbsorptionSpectrophotometryAAS: Coding = {
  code: '0263',
  display: 'Atomic absorption spectrophotometry (AAS)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_IonSelectiveElectrodeISE: Coding = {
  code: '0264',
  display: 'Ion selective electrode (ISE)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_GasChromatographyGC: Coding = {
  code: '0265',
  display: 'Gas chromatography (GC)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_IsoelectricFocusingIEF: Coding = {
  code: '0266',
  display: 'Isoelectric focusing (IEF)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Immunochemiluminescence: Coding = {
  code: '0267',
  display: 'Immunochemiluminescence',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MicroparticleEnzymeImmunoassayMEIA: Coding = {
  code: '0268',
  display: 'Microparticle enzyme immunoassay (MEIA)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ICPMS: Coding = {
  code: '0269',
  display: 'ICP/MS',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ImmunoradiometricAssayIRMA: Coding = {
  code: '0270',
  display: 'Immunoradiometric assay (IRMA)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PhotoOpticalClotDetection: Coding = {
  code: '0271',
  display: 'Photo optical clot detection',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MinimumInhibitoryConcentration: Coding = {
  code: '0272',
  display: 'Minimum Inhibitory Concentration',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ViralGenotypeSusceptibility: Coding = {
  code: '0273',
  display: 'Viral Genotype Susceptibility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ViralPhenotypeSusceptibility: Coding = {
  code: '0274',
  display: 'Viral Phenotype Susceptibility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_GradientStrip: Coding = {
  code: '0275',
  display: 'Gradient Strip',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_MinimumLethalConcentrationMLC: Coding = {
  code: '0275a',
  display: 'Minimum Lethal Concentration (MLC)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SlowMycobacteriaSusceptibility: Coding = {
  code: '0276',
  display: 'Slow Mycobacteria Susceptibility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SerumBactericidalTiter: Coding = {
  code: '0277',
  display: 'Serum bactericidal titer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_AgarScreen: Coding = {
  code: '0278',
  display: 'Agar screen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_DiskInduction: Coding = {
  code: '0279',
  display: 'Disk induction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SusceptibilityTesting: Coding = {
  code: '0280',
  display: 'Susceptibility Testing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Algorithm: Coding = {
  code: 'ALGM',
  display: 'algorithm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Average: Coding = {
  code: 'AVERAGE',
  display: 'average',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_BayesianCalculation: Coding = {
  code: 'BYCL',
  display: 'bayesian calculation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Count: Coding = {
  code: 'COUNT',
  display: 'count',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_GlobalIntrospection: Coding = {
  code: 'GINT',
  display: 'global introspection',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Maxima: Coding = {
  code: 'MAX',
  display: 'maxima',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Median: Coding = {
  code: 'MEDIAN',
  display: 'median',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Minima: Coding = {
  code: 'MIN',
  display: 'minima',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Mode: Coding = {
  code: 'MODE',
  display: 'mode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PCR: Coding = {
  code: 'PCR',
  display: 'PCR',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PopulationStandardDeviation: Coding = {
  code: 'STDEV.P',
  display: 'population standard deviation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SampleStandardDeviation: Coding = {
  code: 'STDEV.S',
  display: 'sample standard deviation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_Sum: Coding = {
  code: 'SUM',
  display: 'sum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_PopulationVariance: Coding = {
  code: 'VARIANCE.P',
  display: 'population variance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_SampleVariance: Coding = {
  code: 'VARIANCE.S',
  display: 'sample variance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_DocumentVerification: Coding = {
  code: 'VDOC',
  display: 'document verification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_RegistryVerification: Coding = {
  code: 'VREG',
  display: 'registry verification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_ElectronicTokenVerification: Coding = {
  code: 'VTOKEN',
  display: 'electronic token verification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
const v3_ObservationMethod_VoiceBasedVerification: Coding = {
  code: 'VVOICE',
  display: 'voice-based verification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationMethod',
};
/**
 *  A code that provides additional detail about the means or technique used to ascertain the observation.  Examples:
 * Blood pressure measurement method: arterial puncture vs. sphygmomanometer (Riva-Rocci), sitting vs. supine position, etc.  OpenIssue:
 * Description copied from Concept Domain of same name.  Must be verified.  Note that the Domain has a full discussion about use of the attribute and constraining that is not appropriate for the code system description.  Needs to be improved.
 */
export const V3ObservationMethod = {
  /**
   * Provides codes for decision methods, initially for assessing the causality of events.
   */
  DecisionObservationMethod: v3_ObservationMethod_DecisionObservationMethod,
  /**
   * A code that provides additional detail about the means or technique used to ascertain the genetic analysis. Example, PCR, Micro Array
   */
  GeneticObservationMethod: v3_ObservationMethod_GeneticObservationMethod,
  /**
   * Provides additional detail about the aggregation methods used to compute the aggregated values for an observation. This is an abstract code.
   */
  ObservationMethodAggregate: v3_ObservationMethod_ObservationMethodAggregate,
  /**
   * VerificationMethod
   */
  VerificationMethod: v3_ObservationMethod_VerificationMethod,
  /**
   * Complement fixation
   */
  ComplementFixation: v3_ObservationMethod_ComplementFixation,
  /**
   * Computed axial tomography
   */
  ComputedAxialTomography: v3_ObservationMethod_ComputedAxialTomography,
  /**
   * Susceptibility, High Level Aminoglycoside Resistance agar test
   */
  HLARAgarTest: v3_ObservationMethod_HLARAgarTest,
  /**
   * Visual, Macroscopic observation
   */
  MacroscopicObservation: v3_ObservationMethod_MacroscopicObservation,
  /**
   * Computed, Magnetic resonance
   */
  MagneticResonance: v3_ObservationMethod_MagneticResonance,
  /**
   * Computed, Morphometry
   */
  Morphometry: v3_ObservationMethod_Morphometry,
  /**
   * Computed, Positron emission tomography
   */
  PositronEmissionTomography: v3_ObservationMethod_PositronEmissionTomography,
  /**
   * SAMHSA drug assay confirmation
   */
  SAMHSAConfirmation: v3_ObservationMethod_SAMHSAConfirmation,
  /**
   * SAMHSA drug assay screening
   */
  SAMHSAScreening: v3_ObservationMethod_SAMHSAScreening,
  /**
   * Serum Neutralization
   */
  SerumNeutralization: v3_ObservationMethod_SerumNeutralization,
  /**
   * Titration
   */
  Titration: v3_ObservationMethod_Titration,
  /**
   * Ultrasound
   */
  Ultrasound: v3_ObservationMethod_Ultrasound,
  /**
   * X-ray crystallography
   */
  XRayCrystallography: v3_ObservationMethod_XRayCrystallography,
  /**
   * Agglutination
   */
  Agglutination: v3_ObservationMethod_Agglutination,
  /**
   * Agglutination, Buffered acidified plate
   */
  BufferedAcidifiedPlateAgglutination: v3_ObservationMethod_BufferedAcidifiedPlateAgglutination,
  /**
   * Agglutination, Card
   */
  CardAgglutination: v3_ObservationMethod_CardAgglutination,
  /**
   * Agglutination, Hemagglutination
   */
  Hemagglutination: v3_ObservationMethod_Hemagglutination,
  /**
   * Agglutination, Hemagglutination inhibition
   */
  HemagglutinationInhibition: v3_ObservationMethod_HemagglutinationInhibition,
  /**
   * Agglutination, Latex
   */
  LatexAgglutination: v3_ObservationMethod_LatexAgglutination,
  /**
   * Agglutination, Plate
   */
  PlateAgglutination: v3_ObservationMethod_PlateAgglutination,
  /**
   * Agglutination, Rapid Plate
   */
  RapidAgglutination: v3_ObservationMethod_RapidAgglutination,
  /**
   * Agglutination, RBC
   */
  RBCAgglutination: v3_ObservationMethod_RBCAgglutination,
  /**
   * Agglutination, Rivanol
   */
  RivanolAgglutination: v3_ObservationMethod_RivanolAgglutination,
  /**
   * Agglutination, Tube
   */
  TubeAgglutination: v3_ObservationMethod_TubeAgglutination,
  /**
   * Bioassay
   */
  Bioassay: v3_ObservationMethod_Bioassay,
  /**
   * Bioassay, Animal Inoculation
   */
  AnimalInoculation: v3_ObservationMethod_AnimalInoculation,
  /**
   * Bioassay, Cytotoxicity
   */
  Cytotoxicity: v3_ObservationMethod_Cytotoxicity,
  /**
   * Bioassay, Embryo Infective Dose 50
   */
  EmbryoInfectiveDose50: v3_ObservationMethod_EmbryoInfectiveDose50,
  /**
   * Bioassay, Embryo Lethal Dose 50
   */
  EmbryoLethalDose50: v3_ObservationMethod_EmbryoLethalDose50,
  /**
   * Bioassay, Mouse intercerebral inoculation
   */
  MouseIntercerebralInoculation: v3_ObservationMethod_MouseIntercerebralInoculation,
  /**
   * Bioassay, qualitative
   */
  BioassayQualitative: v3_ObservationMethod_BioassayQualitative,
  /**
   * Bioassay, quantitative
   */
  BioassayQuantitative: v3_ObservationMethod_BioassayQuantitative,
  /**
   * Chemical
   */
  ChemicalMethod: v3_ObservationMethod_ChemicalMethod,
  /**
   * Chemical, Differential light absorption
   */
  DifferentialLightAbsorptionChemicalTest: v3_ObservationMethod_DifferentialLightAbsorptionChemicalTest,
  /**
   * Chemical, Dipstick
   */
  Dipstick: v3_ObservationMethod_Dipstick,
  /**
   * Chemical, Dipstick colorimetric laboratory test
   */
  DipstickColorimetricLaboratoryTest: v3_ObservationMethod_DipstickColorimetricLaboratoryTest,
  /**
   * Chemical, Test strip
   */
  TestStrip: v3_ObservationMethod_TestStrip,
  /**
   * Chromatography
   */
  Chromatography: v3_ObservationMethod_Chromatography,
  /**
   * Chromatography, Affinity
   */
  AffinityChromatography: v3_ObservationMethod_AffinityChromatography,
  /**
   * Chromatography, Gas liquid
   */
  GasLiquidChromatography: v3_ObservationMethod_GasLiquidChromatography,
  /**
   * Chromatography, High performance liquid
   */
  HighPerformanceLiquidChromatography: v3_ObservationMethod_HighPerformanceLiquidChromatography,
  /**
   * Chromatography, Liquid
   */
  LiquidChromatography: v3_ObservationMethod_LiquidChromatography,
  /**
   * Chromatography, Protein A affinity
   */
  ProteinAAffinityChromatography: v3_ObservationMethod_ProteinAAffinityChromatography,
  /**
   * Coagulation
   */
  Coagulation: v3_ObservationMethod_Coagulation,
  /**
   * Coagulation, Tilt tube
   */
  TiltTubeCoagulationTime: v3_ObservationMethod_TiltTubeCoagulationTime,
  /**
   * Coagulation, Tilt tube reptilase induced
   */
  TiltTubeReptilaseInducedCoagulation: v3_ObservationMethod_TiltTubeReptilaseInducedCoagulation,
  /**
   * Count, Automated
   */
  AutomatedCount: v3_ObservationMethod_AutomatedCount,
  /**
   * Count, Manual
   */
  ManualCellCount: v3_ObservationMethod_ManualCellCount,
  /**
   * Count, Platelet, Rees-Ecker
   */
  PlateletCountReesEcker: v3_ObservationMethod_PlateletCountReesEcker,
  /**
   * Culture, Aerobic
   */
  AerobicCulture: v3_ObservationMethod_AerobicCulture,
  /**
   * Culture, Anaerobic
   */
  AnaerobicCulture: v3_ObservationMethod_AnaerobicCulture,
  /**
   * Culture, Chicken Embryo
   */
  ChickenEmbryoCulture: v3_ObservationMethod_ChickenEmbryoCulture,
  /**
   * Culture, Delayed secondary enrichment
   */
  DelayedSecondaryEnrichment: v3_ObservationMethod_DelayedSecondaryEnrichment,
  /**
   * Culture, Microaerophilic
   */
  MicroaerophilicCulture: v3_ObservationMethod_MicroaerophilicCulture,
  /**
   * Culture, Quantitative microbial, cup
   */
  QuantitativeMicrobialCultureCup: v3_ObservationMethod_QuantitativeMicrobialCultureCup,
  /**
   * Culture, Quantitative microbial, droplet
   */
  QuantitativeMicrobialCultureDroplet: v3_ObservationMethod_QuantitativeMicrobialCultureDroplet,
  /**
   * Culture, Quantitative microbial, filter paper
   */
  QuantitativeMicrobialCultureFilterPaper: v3_ObservationMethod_QuantitativeMicrobialCultureFilterPaper,
  /**
   * Culture, Quantitative microbial, pad
   */
  QuantitativeMicrobialCulturePadCulture: v3_ObservationMethod_QuantitativeMicrobialCulturePadCulture,
  /**
   * Culture, Quantitative microbial, pour plate
   */
  QuantitativeMicrobialCulturePourPlate: v3_ObservationMethod_QuantitativeMicrobialCulturePourPlate,
  /**
   * Culture, Quantitative microbial, surface streak
   */
  QuantitativeMicrobialCultureSurfaceStreak: v3_ObservationMethod_QuantitativeMicrobialCultureSurfaceStreak,
  /**
   * Culture, Somatic Cell
   */
  SomaticCellCulture: v3_ObservationMethod_SomaticCellCulture,
  /**
   * Diffusion, Agar
   */
  AgarDiffusion: v3_ObservationMethod_AgarDiffusion,
  /**
   * Diffusion, Agar Gel Immunodiffusion
   */
  AgarGelImmunodiffusion: v3_ObservationMethod_AgarGelImmunodiffusion,
  /**
   * Electrophoresis
   */
  Electrophoresis: v3_ObservationMethod_Electrophoresis,
  /**
   * Electrophoresis, Agaorse gel
   */
  AgaorseGelElectrophoresis: v3_ObservationMethod_AgaorseGelElectrophoresis,
  /**
   * Electrophoresis, citrate agar
   */
  ElectrophoresisCitrateAgar: v3_ObservationMethod_ElectrophoresisCitrateAgar,
  /**
   * Electrophoresis, Immuno
   */
  Immunoelectrophoresis: v3_ObservationMethod_Immunoelectrophoresis,
  /**
   * Electrophoresis, Polyacrylamide gel
   */
  PolyacrylamideGelElectrophoresis: v3_ObservationMethod_PolyacrylamideGelElectrophoresis,
  /**
   * Electrophoresis, Starch gel
   */
  StarchGelElectrophoresis: v3_ObservationMethod_StarchGelElectrophoresis,
  /**
   * ELISA
   */
  ELISA: v3_ObservationMethod_ELISA,
  /**
   * ELISA, antigen capture
   */
  ELISAAntigenCapture: v3_ObservationMethod_ELISAAntigenCapture,
  /**
   * ELISA, avidin biotin peroxidase complex
   */
  ELISAAvidinBiotinPeroxidaseComplex: v3_ObservationMethod_ELISAAvidinBiotinPeroxidaseComplex,
  /**
   * ELISA, Kinetic
   */
  KineticELISA: v3_ObservationMethod_KineticELISA,
  /**
   * ELISA, peroxidase-antiperoxidase
   */
  ELISAPeroxidaseAntiperoxidase: v3_ObservationMethod_ELISAPeroxidaseAntiperoxidase,
  /**
   * Identification, API 20 Strep
   */
  API20Strep: v3_ObservationMethod_API20Strep,
  /**
   * Identification, API 20A
   */
  API20A: v3_ObservationMethod_API20A,
  /**
   * Identification, API 20C AUX
   */
  API20CAUX: v3_ObservationMethod_API20CAUX,
  /**
   * Identification, API 20E
   */
  API20E: v3_ObservationMethod_API20E,
  /**
   * Identification, API 20NE
   */
  API20NE: v3_ObservationMethod_API20NE,
  /**
   * Identification, API 50 CH
   */
  API50CH: v3_ObservationMethod_API50CH,
  /**
   * Identification, API An-IDENT
   */
  APIAnIDENT: v3_ObservationMethod_APIAnIDENT,
  /**
   * Identification, API Coryne
   */
  APICoryne: v3_ObservationMethod_APICoryne,
  /**
   * Identification, API Rapid 20E
   */
  APIRapid20E: v3_ObservationMethod_APIRapid20E,
  /**
   * Identification, API Staph
   */
  APIStaph: v3_ObservationMethod_APIStaph,
  /**
   * Identification, API ZYM
   */
  APIZYM: v3_ObservationMethod_APIZYM,
  /**
   * Identification, Bacterial
   */
  BacterialIdentification: v3_ObservationMethod_BacterialIdentification,
  /**
   * Identification, mini VIDAS
   */
  MiniVIDAS: v3_ObservationMethod_MiniVIDAS,
  /**
   * Identification, Phage susceptibility typing
   */
  PhageSusceptibilityTyping: v3_ObservationMethod_PhageSusceptibilityTyping,
  /**
   * Identification, Quad-FERM+
   */
  QuadFERMPlus: v3_ObservationMethod_QuadFERMPlus,
  /**
   * Identification, RAPIDEC Staph
   */
  RAPIDECStaph: v3_ObservationMethod_RAPIDECStaph,
  /**
   * Identification, Staphaurex
   */
  Staphaurex: v3_ObservationMethod_Staphaurex,
  /**
   * Identification, VIDAS
   */
  VIDAS: v3_ObservationMethod_VIDAS,
  /**
   * Identification, Vitek
   */
  Vitek: v3_ObservationMethod_Vitek,
  /**
   * Identification, VITEK 2
   */
  VITEK2: v3_ObservationMethod_VITEK2,
  /**
   * Immune stain
   */
  ImmuneStain: v3_ObservationMethod_ImmuneStain,
  /**
   * Immune stain, Immunofluorescent antibody, direct
   */
  ImmunofluorescentAntibodyDirect: v3_ObservationMethod_ImmunofluorescentAntibodyDirect,
  /**
   * Immune stain, Immunofluorescent antibody, indirect
   */
  ImmunofluorescentAntibodyIndirect: v3_ObservationMethod_ImmunofluorescentAntibodyIndirect,
  /**
   * Immune stain, Immunoperoxidase, Avidin-Biotin Complex
   */
  ImmunoperoxidaseAvidinBiotinComplex: v3_ObservationMethod_ImmunoperoxidaseAvidinBiotinComplex,
  /**
   * Immune stain, Immunoperoxidase, Peroxidase anti-peroxidase complex
   */
  ImmunoperoxidasePeroxidaseAntiPeroxidaseComplex: v3_ObservationMethod_ImmunoperoxidasePeroxidaseAntiPeroxidaseComplex,
  /**
   * Immune stain, Immunoperoxidase, Protein A-peroxidase complex
   */
  ImmunoperoxidaseProteinAPeroxidaseComplex: v3_ObservationMethod_ImmunoperoxidaseProteinAPeroxidaseComplex,
  /**
   * Immunoassay
   */
  Immunoassay: v3_ObservationMethod_Immunoassay,
  /**
   * Immunoassay, qualitative, multiple step
   */
  ImmunoassayQualitativeMultipleStep: v3_ObservationMethod_ImmunoassayQualitativeMultipleStep,
  /**
   * Immunoassay, qualitative, single step
   */
  ImmunoassayQualitativeSingleStep: v3_ObservationMethod_ImmunoassayQualitativeSingleStep,
  /**
   * Immunoassay, Radioimmunoassay
   */
  Radioimmunoassay: v3_ObservationMethod_Radioimmunoassay,
  /**
   * Immunoassay, semi-quantitative, multiple step
   */
  ImmunoassaySemiQuantitativeMultipleStep: v3_ObservationMethod_ImmunoassaySemiQuantitativeMultipleStep,
  /**
   * Immunoassay, semi-quantitative, single step
   */
  ImmunoassaySemiQuantitativeSingleStep: v3_ObservationMethod_ImmunoassaySemiQuantitativeSingleStep,
  /**
   * Microscopy
   */
  Microscopy: v3_ObservationMethod_Microscopy,
  /**
   * Microscopy, Darkfield
   */
  DarkfieldMicroscopy: v3_ObservationMethod_DarkfieldMicroscopy,
  /**
   * Microscopy, Electron
   */
  ElectronMicroscopy: v3_ObservationMethod_ElectronMicroscopy,
  /**
   * Microscopy, Electron microscopy tomography
   */
  ElectronMicroscopyTomography: v3_ObservationMethod_ElectronMicroscopyTomography,
  /**
   * Microscopy, Electron, negative stain
   */
  ElectronMicroscopyNegativeStain: v3_ObservationMethod_ElectronMicroscopyNegativeStain,
  /**
   * Microscopy, Electron, thick section transmission
   */
  ElectronMicroscopyThickSection: v3_ObservationMethod_ElectronMicroscopyThickSection,
  /**
   * Microscopy, Electron, thin section transmission
   */
  ElectronMicroscopyThinSection: v3_ObservationMethod_ElectronMicroscopyThinSection,
  /**
   * Microscopy, Light
   */
  MicroscopyLight: v3_ObservationMethod_MicroscopyLight,
  /**
   * Microscopy, Polarized light
   */
  PolarizingLightMicroscopy: v3_ObservationMethod_PolarizingLightMicroscopy,
  /**
   * Microscopy, Scanning electron
   */
  ScanningElectronMicroscopy: v3_ObservationMethod_ScanningElectronMicroscopy,
  /**
   * Microscopy, Transmission electron
   */
  TransmissionElectronMicroscopy: v3_ObservationMethod_TransmissionElectronMicroscopy,
  /**
   * Microscopy, Transparent tape direct examination
   */
  TransparentTapeDirectExamination: v3_ObservationMethod_TransparentTapeDirectExamination,
  /**
   * Molecular, 3 Self-Sustaining Sequence Replication
   */
  VAL3SelfSustainingSequenceReplication: v3_ObservationMethod_VAL3SelfSustainingSequenceReplication,
  /**
   * Molecular, Branched Chain DNA
   */
  BranchedChainDNA: v3_ObservationMethod_BranchedChainDNA,
  /**
   * Molecular, Hybridization Protection Assay
   */
  HybridizationProtectionAssay: v3_ObservationMethod_HybridizationProtectionAssay,
  /**
   * Molecular, Immune blot
   */
  ImmuneBlot: v3_ObservationMethod_ImmuneBlot,
  /**
   * Molecular, In-situ hybridization
   */
  InSituHybridization: v3_ObservationMethod_InSituHybridization,
  /**
   * Molecular, Ligase Chain Reaction
   */
  LigaseChainReaction: v3_ObservationMethod_LigaseChainReaction,
  /**
   * Molecular, Ligation Activated Transcription
   */
  LigationActivatedTranscription: v3_ObservationMethod_LigationActivatedTranscription,
  /**
   * Molecular, Nucleic Acid Probe
   */
  NucleicAcidProbe: v3_ObservationMethod_NucleicAcidProbe,
  /**
   * Molecular, Nucleic acid probe
   */
  NucleicAcidProbe_2: v3_ObservationMethod_NucleicAcidProbe_2,
  /**
   * Molecular, Nucleic acid probe with amplification
   *
   *
   *
   *                         Rationale: Duplicate of code 0126. Use code 0126 instead.
   */
  NucleicAcidProbeWithAmplification: v3_ObservationMethod_NucleicAcidProbeWithAmplification,
  /**
   * Molecular, Nucleic acid probe with target amplification
   */
  NucleicAcidProbeWithTargetAmplification: v3_ObservationMethod_NucleicAcidProbeWithTargetAmplification,
  /**
   * Molecular, Nucleic acid reverse transcription
   */
  NucleicAcidReverseTranscription: v3_ObservationMethod_NucleicAcidReverseTranscription,
  /**
   * Molecular, Nucleic Acid Sequence Based Analysis
   */
  NucleicAcidSequenceBasedAnalysis: v3_ObservationMethod_NucleicAcidSequenceBasedAnalysis,
  /**
   * Molecular, Polymerase chain reaction
   */
  PolymeraseChainReaction: v3_ObservationMethod_PolymeraseChainReaction,
  /**
   * Molecular, Q-Beta Replicase or probe amplification category method
   */
  QBetaReplicaseOrProbeAmplificationCategoryMethod: v3_ObservationMethod_QBetaReplicaseOrProbeAmplificationCategoryMethod,
  /**
   * Molecular, Restriction Fragment Length Polymorphism
   */
  RestrictionFragmentLengthPolymorphism: v3_ObservationMethod_RestrictionFragmentLengthPolymorphism,
  /**
   * Molecular, Southern Blot
   */
  SouthernBlot: v3_ObservationMethod_SouthernBlot,
  /**
   * Molecular, Strand Displacement Amplification
   */
  StrandDisplacementAmplification: v3_ObservationMethod_StrandDisplacementAmplification,
  /**
   * Molecular, Transcription Mediated Amplification
   */
  TranscriptionMediatedAmplification: v3_ObservationMethod_TranscriptionMediatedAmplification,
  /**
   * Molecular, Western Blot
   */
  WesternBlot: v3_ObservationMethod_WesternBlot,
  /**
   * Precipitation, Flocculation
   */
  Flocculation: v3_ObservationMethod_Flocculation,
  /**
   * Precipitation, Immune precipitation
   */
  ImmunePrecipitation: v3_ObservationMethod_ImmunePrecipitation,
  /**
   * Precipitation, Milk ring test
   */
  MilkRingTest: v3_ObservationMethod_MilkRingTest,
  /**
   * Precipitation, Precipitin
   */
  Precipitin: v3_ObservationMethod_Precipitin,
  /**
   * Stain, Acid fast
   */
  AcidFastStain: v3_ObservationMethod_AcidFastStain,
  /**
   * Stain, Acid fast, fluorochrome
   */
  AcidFastStainFluorochrome: v3_ObservationMethod_AcidFastStainFluorochrome,
  /**
   * Stain, Acid fast, Kinyoun's cold carbolfuchsin
   */
  AcidFastStainKinyounQuoteSColdCarbolfuchsin: v3_ObservationMethod_AcidFastStainKinyounQuoteSColdCarbolfuchsin,
  /**
   * Stain, Acid fast, Ziehl-Neelsen
   */
  AcidFastStainZiehlNeelsen: v3_ObservationMethod_AcidFastStainZiehlNeelsen,
  /**
   * Stain, Acid phosphatase
   */
  AcidPhosphataseStain: v3_ObservationMethod_AcidPhosphataseStain,
  /**
   * Stain, Acridine orange
   */
  AcridineOrangeStain: v3_ObservationMethod_AcridineOrangeStain,
  /**
   * Stain, Active brilliant orange KH
   */
  ActiveBrilliantOrangeKHStain: v3_ObservationMethod_ActiveBrilliantOrangeKHStain,
  /**
   * Stain, Alazarin red S
   */
  AlazarinRedSStain: v3_ObservationMethod_AlazarinRedSStain,
  /**
   * Stain, Alcian blue
   */
  AlcianBlueStain: v3_ObservationMethod_AlcianBlueStain,
  /**
   * Stain, Alcian blue with Periodic acid Schiff
   */
  AlcianBlueWithPeriodicAcidSchiffStain: v3_ObservationMethod_AlcianBlueWithPeriodicAcidSchiffStain,
  /**
   * Stain, Argentaffin
   */
  ArgentaffinStain: v3_ObservationMethod_ArgentaffinStain,
  /**
   * Stain, Argentaffin silver
   */
  ArgentaffinSilverStain: v3_ObservationMethod_ArgentaffinSilverStain,
  /**
   * Stain, Azure-eosin
   */
  AzureEosinStain: v3_ObservationMethod_AzureEosinStain,
  /**
   * Stain, Basic Fuschin
   */
  BasicFuschinStain: v3_ObservationMethod_BasicFuschinStain,
  /**
   * Stain, Bennhold
   */
  BennholdStain: v3_ObservationMethod_BennholdStain,
  /**
   * Stain, Bennhold's Congo red
   */
  BennholdQuoteSCongoRedStain: v3_ObservationMethod_BennholdQuoteSCongoRedStain,
  /**
   * Stain, Bielschowsky
   */
  BielschowskyStain: v3_ObservationMethod_BielschowskyStain,
  /**
   * Stain, Bielschowsky's silver
   */
  BielschowskyQuoteSSilverStain: v3_ObservationMethod_BielschowskyQuoteSSilverStain,
  /**
   * Stain, Bleach
   */
  BleachStain: v3_ObservationMethod_BleachStain,
  /**
   * Stain, Bodian
   */
  BodianStain: v3_ObservationMethod_BodianStain,
  /**
   * Stain, Brown-Brenn
   */
  BrownBrennStain: v3_ObservationMethod_BrownBrennStain,
  /**
   * Stain, Butyrate-esterase
   */
  ButyrateEsteraseStain: v3_ObservationMethod_ButyrateEsteraseStain,
  /**
   * Stain, Calcofluor white fluorescent
   */
  CalcofluorWhiteFluorescentStain: v3_ObservationMethod_CalcofluorWhiteFluorescentStain,
  /**
   * Stain, Carbol-fuchsin
   */
  CarbolFuchsinStain: v3_ObservationMethod_CarbolFuchsinStain,
  /**
   * Stain, Carmine
   */
  CarmineStain: v3_ObservationMethod_CarmineStain,
  /**
   * Stain, Churukian-Schenk
   */
  ChurukianSchenkStain: v3_ObservationMethod_ChurukianSchenkStain,
  /**
   * Stain, Congo red
   */
  CongoRedStain: v3_ObservationMethod_CongoRedStain,
  /**
   * Stain, Cresyl echt violet
   */
  CresylEchtVioletStain: v3_ObservationMethod_CresylEchtVioletStain,
  /**
   * Stain, Crystal violet
   */
  CrystalVioletStain: v3_ObservationMethod_CrystalVioletStain,
  /**
   * Stain, De Galantha
   */
  DeGalanthaStain: v3_ObservationMethod_DeGalanthaStain,
  /**
   * Stain, Dieterle silver impregnation
   */
  DieterleSilverImpregnationStain: v3_ObservationMethod_DieterleSilverImpregnationStain,
  /**
   * Stain, Fite-Farco
   */
  FiteFarcoStain: v3_ObservationMethod_FiteFarcoStain,
  /**
   * Stain, Fontana-Masson silver
   */
  FontanaMassonSilverStain: v3_ObservationMethod_FontanaMassonSilverStain,
  /**
   * Stain, Fouchet
   */
  FouchetStain: v3_ObservationMethod_FouchetStain,
  /**
   * Stain, Gomori
   */
  GomoriStain: v3_ObservationMethod_GomoriStain,
  /**
   * Stain, Gomori methenamine silver
   */
  GomoriMethenamineSilverStain: v3_ObservationMethod_GomoriMethenamineSilverStain,
  /**
   * Stain, Gomori-Wheatly trichrome
   */
  GomoriWheatlyTrichromeStain: v3_ObservationMethod_GomoriWheatlyTrichromeStain,
  /**
   * Stain, Gridley
   */
  GridleyStain: v3_ObservationMethod_GridleyStain,
  /**
   * Stain, Grimelius silver
   */
  GrimeliusSilverStain: v3_ObservationMethod_GrimeliusSilverStain,
  /**
   * Stain, Grocott
   */
  GrocottStain: v3_ObservationMethod_GrocottStain,
  /**
   * Stain, Grocott methenamine silver
   */
  GrocottMethenamineSilverStain: v3_ObservationMethod_GrocottMethenamineSilverStain,
  /**
   * Stain, Hale's colloidal ferric oxide
   */
  HaleQuoteSColloidalFerricOxideStain: v3_ObservationMethod_HaleQuoteSColloidalFerricOxideStain,
  /**
   * Stain, Hale's colloidal iron
   */
  HaleQuoteSColloidalIronStain: v3_ObservationMethod_HaleQuoteSColloidalIronStain,
  /**
   * Stain, Hansel
   */
  HanselStain: v3_ObservationMethod_HanselStain,
  /**
   * Stain, Harris regressive hematoxylin and eosin
   */
  HarrisRegressiveHematoxylinAndEosinStain: v3_ObservationMethod_HarrisRegressiveHematoxylinAndEosinStain,
  /**
   * Stain, Hematoxylin and eosin
   */
  HematoxylinAndEosinStain: v3_ObservationMethod_HematoxylinAndEosinStain,
  /**
   * Stain, Highman
   */
  HighmanStain: v3_ObservationMethod_HighmanStain,
  /**
   * Stain, Holzer
   */
  HolzerStain: v3_ObservationMethod_HolzerStain,
  /**
   * Stain, Iron hematoxylin
   */
  IronHematoxylinStain: v3_ObservationMethod_IronHematoxylinStain,
  /**
   * Stain, Jones
   */
  JonesStain: v3_ObservationMethod_JonesStain,
  /**
   * Stain, Jones methenamine silver
   */
  JonesMethenamineSilverStain: v3_ObservationMethod_JonesMethenamineSilverStain,
  /**
   * Stain, Kossa
   */
  KossaStain: v3_ObservationMethod_KossaStain,
  /**
   * Stain, Lawson-Van Gieson
   */
  LawsonVanGiesonStain: v3_ObservationMethod_LawsonVanGiesonStain,
  /**
   * Stain, Loeffler methylene blue
   */
  LoefflerMethyleneBlueStain: v3_ObservationMethod_LoefflerMethyleneBlueStain,
  /**
   * Stain, Luxol fast blue with cresyl violet
   */
  LuxolFastBlueWithCresylVioletStain: v3_ObservationMethod_LuxolFastBlueWithCresylVioletStain,
  /**
   * Stain, Luxol fast blue with Periodic acid-Schiff
   */
  LuxolFastBlueWithPeriodicAcidSchiffStain: v3_ObservationMethod_LuxolFastBlueWithPeriodicAcidSchiffStain,
  /**
   * Stain, MacNeal's tetrachrome blood
   */
  MacNealQuoteSTetrachromeBloodStain: v3_ObservationMethod_MacNealQuoteSTetrachromeBloodStain,
  /**
   * Stain, Mallory-Heidenhain
   */
  MalloryHeidenhainStain: v3_ObservationMethod_MalloryHeidenhainStain,
  /**
   * Stain, Masson trichrome
   */
  MassonTrichromeStain: v3_ObservationMethod_MassonTrichromeStain,
  /**
   * Stain, Mayer mucicarmine
   */
  MayerMucicarmineStain: v3_ObservationMethod_MayerMucicarmineStain,
  /**
   * Stain, Mayers progressive hematoxylin and eosin
   */
  MayersProgressiveHematoxylinAndEosinStain: v3_ObservationMethod_MayersProgressiveHematoxylinAndEosinStain,
  /**
   * Stain, May-Grunwald Giemsa
   */
  MayGrunwaldGiemsaStain: v3_ObservationMethod_MayGrunwaldGiemsaStain,
  /**
   * Stain, Methyl green
   */
  MethylGreenStain: v3_ObservationMethod_MethylGreenStain,
  /**
   * Stain, Methyl green pyronin
   */
  MethylGreenPyroninStain: v3_ObservationMethod_MethylGreenPyroninStain,
  /**
   * Stain, Modified Gomori-Wheatly trichrome
   */
  ModifiedGomoriWheatlyTrichromeStain: v3_ObservationMethod_ModifiedGomoriWheatlyTrichromeStain,
  /**
   * Stain, Modified Masson trichrome
   */
  ModifiedMassonTrichromeStain: v3_ObservationMethod_ModifiedMassonTrichromeStain,
  /**
   * Stain, Modified trichrome
   */
  ModifiedTrichromeStain: v3_ObservationMethod_ModifiedTrichromeStain,
  /**
   * Stain, Movat pentachrome
   */
  MovatPentachromeStain: v3_ObservationMethod_MovatPentachromeStain,
  /**
   * Stain, Mucicarmine
   */
  MucicarmineStain: v3_ObservationMethod_MucicarmineStain,
  /**
   * Stain, Neutral red
   */
  NeutralRedStain: v3_ObservationMethod_NeutralRedStain,
  /**
   * Stain, Night blue
   */
  NightBlueStain: v3_ObservationMethod_NightBlueStain,
  /**
   * Stain, Non-specific esterase
   */
  NonSpecificEsteraseStain: v3_ObservationMethod_NonSpecificEsteraseStain,
  /**
   * Stain, Oil red-O
   */
  OilRedOStain: v3_ObservationMethod_OilRedOStain,
  /**
   * Stain, Orcein
   */
  OrceinStain: v3_ObservationMethod_OrceinStain,
  /**
   * Stain, Perls'
   */
  PerlsQuoteStain: v3_ObservationMethod_PerlsQuoteStain,
  /**
   * Stain, Phosphotungstic acid-hematoxylin
   */
  PhosphotungsticAcidHematoxylinStain: v3_ObservationMethod_PhosphotungsticAcidHematoxylinStain,
  /**
   * Stain, Potassium ferrocyanide
   */
  PotassiumFerrocyanideStain: v3_ObservationMethod_PotassiumFerrocyanideStain,
  /**
   * Stain, Prussian blue
   */
  PrussianBlueStain: v3_ObservationMethod_PrussianBlueStain,
  /**
   * Stain, Putchler modified Bennhold
   */
  PutchlerModifiedBennholdStain: v3_ObservationMethod_PutchlerModifiedBennholdStain,
  /**
   * Stain, Quinacrine fluorescent
   */
  QuinacrineFluorescentStain: v3_ObservationMethod_QuinacrineFluorescentStain,
  /**
   * Stain, Reticulin
   */
  ReticulinStain: v3_ObservationMethod_ReticulinStain,
  /**
   * Stain, Rhodamine
   */
  RhodamineStain: v3_ObservationMethod_RhodamineStain,
  /**
   * Stain, Safranin
   */
  SafraninStain: v3_ObservationMethod_SafraninStain,
  /**
   * Stain, Schmorl
   */
  SchmorlStain: v3_ObservationMethod_SchmorlStain,
  /**
   * Stain, Seiver-Munger
   */
  SeiverMungerStain: v3_ObservationMethod_SeiverMungerStain,
  /**
   * Stain, Silver
   */
  SilverStain: v3_ObservationMethod_SilverStain,
  /**
   * Stain, Specific esterase
   */
  SpecificEsteraseStain: v3_ObservationMethod_SpecificEsteraseStain,
  /**
   * Stain, Steiner silver
   */
  SteinerSilverStain: v3_ObservationMethod_SteinerSilverStain,
  /**
   * Stain, Sudan III
   */
  SudanIIIStain: v3_ObservationMethod_SudanIIIStain,
  /**
   * Stain, Sudan IVI
   */
  SudanIVIStain: v3_ObservationMethod_SudanIVIStain,
  /**
   * Stain, Sulfated alcian blue
   */
  SulfatedAlcianBlueStain: v3_ObservationMethod_SulfatedAlcianBlueStain,
  /**
   * Stain, Supravital
   */
  SupravitalStain: v3_ObservationMethod_SupravitalStain,
  /**
   * Stain, Thioflavine-S
   */
  ThioflavineSStain: v3_ObservationMethod_ThioflavineSStain,
  /**
   * Stain, Three micron Giemsa
   */
  ThreeMicronGiemsaStain: v3_ObservationMethod_ThreeMicronGiemsaStain,
  /**
   * Stain, Vassar-Culling
   */
  VassarCullingStain: v3_ObservationMethod_VassarCullingStain,
  /**
   * Stain, Vital
   */
  VitalStain: v3_ObservationMethod_VitalStain,
  /**
   * Stain, von Kossa
   */
  VonKossaStain: v3_ObservationMethod_VonKossaStain,
  /**
   * Susceptibility, Antibiotic sensitivity, disk
   */
  AntibioticSensitivityDisk: v3_ObservationMethod_AntibioticSensitivityDisk,
  /**
   * Susceptibility, BACTEC susceptibility test
   */
  BACTECSusceptibilityTest: v3_ObservationMethod_BACTECSusceptibilityTest,
  /**
   * Susceptibility, Disk dilution
   */
  DiskDilution: v3_ObservationMethod_DiskDilution,
  /**
   * Susceptibility, Minimum bactericidal concentration, macrodilution
   */
  MinimumBactericidalConcentrationTestMacrodilution: v3_ObservationMethod_MinimumBactericidalConcentrationTestMacrodilution,
  /**
   * Susceptibility, Minimum bactericidal concentration, microdilution
   */
  MinimumBactericidalConcentrationTestMicrodilution: v3_ObservationMethod_MinimumBactericidalConcentrationTestMicrodilution,
  /**
   * Susceptibility, Minimum Inhibitory concentration, macrodilution
   */
  MinimumInhibitoryConcentrationMacrodilution: v3_ObservationMethod_MinimumInhibitoryConcentrationMacrodilution,
  /**
   * Susceptibility, Minimum Inhibitory concentration, microdilution
   */
  MinimumInhibitoryConcentrationMicrodilution: v3_ObservationMethod_MinimumInhibitoryConcentrationMicrodilution,
  /**
   * Turbidometric
   */
  Turbidometric: v3_ObservationMethod_Turbidometric,
  /**
   * Turbidometric, Refractometric
   */
  Refractometric: v3_ObservationMethod_Refractometric,
  /**
   * Chromatography, Thin Layer
   */
  ThinLayerChromatographyTLC: v3_ObservationMethod_ThinLayerChromatographyTLC,
  /**
   * Immunoassay, enzyme-multiplied technique (EMIT)
   */
  EMIT: v3_ObservationMethod_EMIT,
  /**
   * Flow Cytometry
   */
  FlowCytometryFC: v3_ObservationMethod_FlowCytometryFC,
  /**
   * Radial Immunodiffusion
   */
  RadialImmunodiffusionRID: v3_ObservationMethod_RadialImmunodiffusionRID,
  /**
   * Immunoassay, Fluorescence Polarization
   */
  FluorescencePolarizationImmunoassayFPIA: v3_ObservationMethod_FluorescencePolarizationImmunoassayFPIA,
  /**
   * Electrophoresis, Immunofixation
   */
  ImmunofixationElectrophoresisIFE: v3_ObservationMethod_ImmunofixationElectrophoresisIFE,
  /**
   * Dialysis, Direct Equilibrium
   */
  EquilibriumDialysis: v3_ObservationMethod_EquilibriumDialysis,
  /**
   * Acid Elution, Kleihauer-Betke Method
   */
  KleihauerBetkeAcidElution: v3_ObservationMethod_KleihauerBetkeAcidElution,
  /**
   * Immunofluorescence, Anti-Complement
   */
  AntiComplementImmunofluorescenceACIF: v3_ObservationMethod_AntiComplementImmunofluorescenceACIF,
  /**
   * Gas Chromatography/Mass Spectroscopy
   */
  GCMS: v3_ObservationMethod_GCMS,
  /**
   * Light Scatter, Nephelometry
   */
  Nephelometry: v3_ObservationMethod_Nephelometry,
  /**
   * Immunoassay, IgE Antibody Test
   */
  IgEImmunoassayAntibody: v3_ObservationMethod_IgEImmunoassayAntibody,
  /**
   * Lymphocyte Microcytotoxicity Assay
   */
  LymphocyteMicrocytotoxicityAssay: v3_ObservationMethod_LymphocyteMicrocytotoxicityAssay,
  /**
   * Spectrophotometry
   */
  Spectrophotometry: v3_ObservationMethod_Spectrophotometry,
  /**
   * Spectrophotometry, Atomic Absorption
   */
  AtomicAbsorptionSpectrophotometryAAS: v3_ObservationMethod_AtomicAbsorptionSpectrophotometryAAS,
  /**
   * Electrochemical, Ion Selective Electrode
   */
  IonSelectiveElectrodeISE: v3_ObservationMethod_IonSelectiveElectrodeISE,
  /**
   * Chromatography, Gas
   */
  GasChromatographyGC: v3_ObservationMethod_GasChromatographyGC,
  /**
   * Isoelectric Focusing
   */
  IsoelectricFocusingIEF: v3_ObservationMethod_IsoelectricFocusingIEF,
  /**
   * Immunoassay, Chemiluminescent
   */
  Immunochemiluminescence: v3_ObservationMethod_Immunochemiluminescence,
  /**
   * Immunoassay, Microparticle Enzyme
   */
  MicroparticleEnzymeImmunoassayMEIA: v3_ObservationMethod_MicroparticleEnzymeImmunoassayMEIA,
  /**
   * Inductively-Coupled Plasma/Mass Spectrometry
   */
  ICPMS: v3_ObservationMethod_ICPMS,
  /**
   * Immunoassay, Immunoradiometric Assay
   */
  ImmunoradiometricAssayIRMA: v3_ObservationMethod_ImmunoradiometricAssayIRMA,
  /**
   * Coagulation, Photo Optical Clot Detection
   */
  PhotoOpticalClotDetection: v3_ObservationMethod_PhotoOpticalClotDetection,
  /**
   * Testing to measure the minimum concentration of the antibacterial agent in a given culture medium below which bacterial growth is not inhibited.
   */
  MinimumInhibitoryConcentration: v3_ObservationMethod_MinimumInhibitoryConcentration,
  /**
   * Viral Genotype Susceptibility
   */
  ViralGenotypeSusceptibility: v3_ObservationMethod_ViralGenotypeSusceptibility,
  /**
   * Viral Phenotype Susceptibility
   */
  ViralPhenotypeSusceptibility: v3_ObservationMethod_ViralPhenotypeSusceptibility,
  /**
   * Gradient Strip
   */
  GradientStrip: v3_ObservationMethod_GradientStrip,
  /**
   * Minimum Lethal Concentration (MLC)
   */
  MinimumLethalConcentrationMLC: v3_ObservationMethod_MinimumLethalConcentrationMLC,
  /**
   * Testing to measure the minimum concentration of the antibacterial agent in a given culture medium below which bacterial growth is not inhibited.
   */
  SlowMycobacteriaSusceptibility: v3_ObservationMethod_SlowMycobacteriaSusceptibility,
  /**
   * Serum bactericidal titer
   */
  SerumBactericidalTiter: v3_ObservationMethod_SerumBactericidalTiter,
  /**
   * Agar screen
   */
  AgarScreen: v3_ObservationMethod_AgarScreen,
  /**
   * Disk induction
   */
  DiskInduction: v3_ObservationMethod_DiskInduction,
  /**
   * Test methods designed to determine a microorganismaTMs susceptibility to being killed by an antibiotic.
   */
  SusceptibilityTesting: v3_ObservationMethod_SusceptibilityTesting,
  /**
   * Reaching a decision through the application of an algorithm designed to weigh the different factors involved.
   */
  Algorithm: v3_ObservationMethod_Algorithm,
  /**
   * Average of non-null values in the referenced set of values
   */
  Average: v3_ObservationMethod_Average,
  /**
   * Reaching a decision through the use of Bayesian statistical analysis.
   */
  BayesianCalculation: v3_ObservationMethod_BayesianCalculation,
  /**
   * Count of non-null values in the referenced set of values
   */
  Count: v3_ObservationMethod_Count,
  /**
   * Reaching a decision by consideration of the totality of factors involved in order to reach a judgement.
   */
  GlobalIntrospection: v3_ObservationMethod_GlobalIntrospection,
  /**
   * Largest of all non-null values in the referenced set of values.
   */
  Maxima: v3_ObservationMethod_Maxima,
  /**
   * The median of all non-null values in the referenced set of values.
   */
  Median: v3_ObservationMethod_Median,
  /**
   * Smallest of all non-null values in the referenced set of values.
   */
  Minima: v3_ObservationMethod_Minima,
  /**
   * The most common value of all non-null values in the referenced set of values.
   */
  Mode: v3_ObservationMethod_Mode,
  /**
   * Description: Polymerase Chain Reaction
   */
  PCR: v3_ObservationMethod_PCR,
  /**
   * Standard Deviation of the values in the referenced set of values, computed over the population.
   */
  PopulationStandardDeviation: v3_ObservationMethod_PopulationStandardDeviation,
  /**
   * Standard Deviation of the values in the referenced set of values, computed over a sample of the population.
   */
  SampleStandardDeviation: v3_ObservationMethod_SampleStandardDeviation,
  /**
   * Sum of non-null values in the referenced set of values
   */
  Sum: v3_ObservationMethod_Sum,
  /**
   * Variance of the values in the referenced set of values, computed over the population.
   */
  PopulationVariance: v3_ObservationMethod_PopulationVariance,
  /**
   * Variance of the values in the referenced set of values, computed over a sample of the population.
   */
  SampleVariance: v3_ObservationMethod_SampleVariance,
  /**
   * Verification by means of document.
   *
   *
   *                            Example: Fax, letter, attachment to e-mail.
   */
  DocumentVerification: v3_ObservationMethod_DocumentVerification,
  /**
   * verification by means of  a response to an electronic query
   *
   *
   *                            Example: query message to a Covered Party registry application or Coverage Administrator.
   */
  RegistryVerification: v3_ObservationMethod_RegistryVerification,
  /**
   * Verification by means of electronic token.
   *
   *
   *                            Example: smartcard, magnetic swipe card, RFID device.
   */
  ElectronicTokenVerification: v3_ObservationMethod_ElectronicTokenVerification,
  /**
   * Verification by means of voice.
   *
   *
   *                            Example: By speaking with or calling the Coverage Administrator or Covered Party
   */
  VoiceBasedVerification: v3_ObservationMethod_VoiceBasedVerification,
};
/**
 *  Identifies the kinds of observations that can be performed
 */
export const V3ObservationType = {
  /**
   * ActAdministrativeAuthorizationDetectedIssueCode
   */
  ActAdministrativeAuthorizationDetectedIssueCode: v3_ActCode_ActAdministrativeAuthorizationDetectedIssueCode,
  /**
   * Identifies types of detectyed issues for Act class "ALRT" for the administrative and patient administrative acts domains.
   */
  ActAdministrativeDetectedIssueCode: v3_ActCode_ActAdministrativeDetectedIssueCode,
  /**
   * ActAdministrativeRuleDetectedIssueCode
   */
  ActAdministrativeRuleDetectedIssueCode: v3_ActCode_ActAdministrativeRuleDetectedIssueCode,
  /**
   * Description:Provides a categorization for annotations recorded directly against the patient .
   */
  ActPatientAnnotationType: v3_ActCode_ActPatientAnnotationType,
  /**
   * Identifies the type of observation that is made about a specimen that may affect its processing, analysis or further result interpretation
   */
  ActSpecObsCode: v3_ActCode_ActSpecObsCode,
  /**
   * Identifies types of detected issues regarding the administration or supply of an item to a patient.
   */
  ActSuppliedItemDetectedIssueCode: v3_ActCode_ActSuppliedItemDetectedIssueCode,
  /**
   * Administration of the proposed therapy may be inappropriate or contraindicated as proposed
   */
  AdministrationDetectedIssueCode: v3_ActCode_AdministrationDetectedIssueCode,
  /**
   * AnnotationType
   */
  AnnotationType: v3_ActCode_AnnotationType,
  /**
   * AppropriatenessDetectedIssueCode
   */
  AppropriatenessDetectedIssueCode: v3_ActCode_AppropriatenessDetectedIssueCode,
  /**
   * Code for the mechanism by which disease was acquired by the living subject involved in the public health case. Includes sexually transmitted, airborne, bloodborne, vectorborne, foodborne, zoonotic, nosocomial, mechanical, dermal, congenital, environmental exposure, indeterminate.
   */
  CaseTransmissionMode: v3_ActCode_CaseTransmissionMode,
  /**
   * ECGObservationSeriesType
   */
  ECGObservationSeriesType: v3_ActCode_ECGObservationSeriesType,
  /**
   * FDA label data
   */
  FDALabelData: v3_ActCode_FDALabelData,
  /**
   * Description: None provided
   */
  GeneticObservationType: v3_ActCode_GeneticObservationType,
  /**
   * Description: Observation codes which describe characteristics of the immunization material.
   */
  ImmunizationObservationType: v3_ActCode_ImmunizationObservationType,
  /**
   * A code that is used to indicate the type of case safety report received from sender. The current code example reference is from the International Conference on Harmonisation (ICH) Expert Workgroup guideline on Clinical Safety Data Management: Data Elements for Transmission of Individual Case Safety Reports. The unknown/unavailable option allows the transmission of information from a secondary sender where the initial sender did not specify the type of report.
   *
   *                         Example concepts include: Spontaneous, Report from study, Other.
   */
  IndividualCaseSafetyReportType: v3_ActCode_IndividualCaseSafetyReportType,
  /**
   * InteractionDetectedIssueCode
   */
  InteractionDetectedIssueCode: v3_ActCode_InteractionDetectedIssueCode,
  /**
   * Definition:The set of LOINC codes for the act of determining the period of time that has elapsed since an entity was born or created.
   */
  LOINCObservationActContextAgeType: v3_ActCode_LOINCObservationActContextAgeType,
  /**
   * MedicationObservationType
   */
  MedicationObservationType: v3_ActCode_MedicationObservationType,
  /**
   * Distinguishes the kinds of coded observations that could be the trigger for clinical issue detection. These are observations that are not measurable, but instead can be defined with codes. Coded observation types include: Allergy, Intolerance, Medical Condition, Pregnancy status, etc.
   */
  ObservationIssueTriggerCodedObservationType: v3_ActCode_ObservationIssueTriggerCodedObservationType,
  /**
   * Codes used to define various metadata aspects of a health quality measure.
   */
  ObservationQualityMeasureAttribute: v3_ActCode_ObservationQualityMeasureAttribute,
  /**
   * ObservationSequenceType
   */
  ObservationSequenceType: v3_ActCode_ObservationSequenceType,
  /**
   * ObservationSeriesType
   */
  ObservationSeriesType: v3_ActCode_ObservationSeriesType,
  /**
   * Identifies the kinds of observations that can be performed
   */
  ObservationType: v3_ActCode_ObservationType,
  /**
   * Description: Reporting codes that are related to an immunization event.
   */
  PatientImmunizationRelatedObservationType: v3_ActCode_PatientImmunizationRelatedObservationType,
  /**
   * Observation types for specifying criteria used to assert that a subject is included in a particular population.
   */
  PopulationInclusionObservationType: v3_ActCode_PopulationInclusionObservationType,
  /**
   * Types of observations that can be made about Preferences.
   */
  PreferenceObservationType: v3_ActCode_PreferenceObservationType,
  /**
   * Supplying the product at this time may be inappropriate or indicate compliance issues with the associated therapy
   */
  SupplyDetectedIssueCode: v3_ActCode_SupplyDetectedIssueCode,
  /**
   * Proposed therapy may be inappropriate or ineffective based on the proposed start or end time.
   */
  TimingDetectedIssueCode: v3_ActCode_TimingDetectedIssueCode,
  /**
   * Definition:Estimated age.
   */
  AgePatientQnEst: v3_ActCode_AgePatientQnEst,
  /**
   * Definition:Reported age.
   */
  AgePatientQnReported: v3_ActCode_AgePatientQnReported,
  /**
   * Definition:Calculated age.
   */
  AgePatientQnCalc: v3_ActCode_AgePatientQnCalc,
  /**
   * Definition:General specification of age with no implied method of determination.
   */
  AgePatientQnDefinition: v3_ActCode_AgePatientQnDefinition,
  /**
   * Definition:Age at onset of associated adverse event; no implied method of determination.
   */
  AgeAtOnsetOfAdverseEvent: v3_ActCode_AgeAtOnsetOfAdverseEvent,
  /**
   * Description:The proposed therapy is frequently misused or abused and therefore should be used with caution and/or monitoring.
   */
  CommonlyAbusedMisusedAlert: v3_ActCode_CommonlyAbusedMisusedAlert,
  /**
   * Proposed therapy is outside of the standard practice for an adult patient.
   */
  AdultAlert: v3_ActCode_AdultAlert,
  /**
   * Admitting diagnosis are the diagnoses documented  for administrative purposes as the basis for a hospital admission.
   */
  AdmittingDiagnosis: v3_ActCode_AdmittingDiagnosis,
  /**
   * Indicates that the observation is of an unexpected negative occurrence in the subject suspected to result from the subject's exposure to one or more agents.  Observation values would be the symptom resulting from the reaction.
   */
  AdverseReaction: v3_ActCode_AdverseReaction,
  /**
   * Proposed therapy may be inappropriate or contraindicated due to patient age
   */
  AgeAlert: v3_ActCode_AgeAlert,
  /**
   * Indicates that the observation is carrying out an aggregation calculation, contained in the value element.
   */
  AggregateMeasureObservation: v3_ActCode_AggregateMeasureObservation,
  /**
   * Communication of an agent from a living subject or environmental source to a living subject through indirect contact via oral or nasal inhalation.
   */
  AirborneTransmission: v3_ActCode_AirborneTransmission,
  /**
   * Hypersensitivity to an agent caused by an immunologic response to an initial exposure
   */
  Allergy: v3_ActCode_Allergy,
  /**
   * Proposed therapy may be inappropriate or contraindicated because of a recorded patient allergy to the proposed product.  (Allergies are immune based reactions.)
   */
  AllergyAlert: v3_ActCode_AllergyAlert,
  /**
   * Definition:The requested action has already been performed and so this request has no effect
   */
  AlreadyPerformed: v3_ActCode_AlreadyPerformed,
  /**
   * Definition:Proposed therapy may be inappropriate or ineffective because the end of administration is too close to another planned therapy.
   */
  EndTooLateAlert: v3_ActCode_EndTooLateAlert,
  /**
   * Definition:Proposed therapy may be inappropriate or ineffective because the start of administration is too late after the onset of the condition.
   */
  StartTooLateAlert: v3_ActCode_StartTooLateAlert,
  /**
   * Communication of an agent from one animal to another proximate animal.
   */
  AnimalToAnimalTransmission: v3_ActCode_AnimalToAnimalTransmission,
  /**
   * Communication of an agent from an animal to a proximate person.
   */
  AnimalToHumanTransmission: v3_ActCode_AnimalToHumanTransmission,
  /**
   * Description:A note that is specific to a patient's diagnostic images, either historical, current or planned.
   */
  DiagnosticImageNote: v3_ActCode_DiagnosticImageNote,
  /**
   * Description:A general or uncategorized note.
   */
  GeneralNote: v3_ActCode_GeneralNote,
  /**
   * A note that is specific to a patient's immunizations, either historical, current or planned.
   */
  ImmunizationNote: v3_ActCode_ImmunizationNote,
  /**
   * Description:A note that is specific to a patient's laboratory results, either historical, current or planned.
   */
  LaboratoryNote: v3_ActCode_LaboratoryNote,
  /**
   * Description:A note that is specific to a patient's medications, either historical, current or planned.
   */
  MedicationNote: v3_ActCode_MedicationNote,
  /**
   * Describes the artificial blood identifier that is associated with the specimen.
   */
  ActSpecObsArtBldCode: v3_ActCode_ActSpecObsArtBldCode,
  /**
   * Description:Refines classCode OBS to indicate an observation in which observation.value contains a finding or other nominalized statement, where the encoded information in Observation.value is not altered by Observation.code.  For instance, observation.code="ASSERTION" and observation.value="fracture of femur present" is an assertion of a clinical finding of femur fracture.
   */
  Assertion: v3_ActCode_Assertion,
  /**
   * The dilution of a sample performed by automated equipment.  The value is specified by the equipment
   */
  AutoHighDilution: v3_ActCode_AutoHighDilution,
  /**
   * The dilution of a sample performed by automated equipment.  The value is specified by the equipment
   */
  AutoLowDilution: v3_ActCode_AutoLowDilution,
  /**
   * The available quantity of specimen.   This is the current quantity minus any planned consumption (e.g., tests that are planned)
   */
  AvailableVolume: v3_ActCode_AvailableVolume,
  /**
   * Communication of an agent from one living subject to another living subject through direct contact with any body fluid.
   */
  BodyFluidContactTransmission: v3_ActCode_BodyFluidContactTransmission,
  /**
   * Communication of an agent to a living subject through direct contact with blood or blood products whether the contact with blood is part of  a therapeutic procedure or not.
   */
  BloodBorneTransmission: v3_ActCode_BloodBorneTransmission,
  /**
   * Description:A local business rule relating multiple elements has been violated.
   */
  BusinessConstraintViolation: v3_ActCode_BusinessConstraintViolation,
  /**
   * Definition:An observation that provides a characterization of the level of harm to an investigation subject as a result of a reaction or event.
   */
  CaseSeriousnessCriteria: v3_ActCode_CaseSeriousnessCriteria,
  /**
   * An observation that states whether the disease was likely acquired outside the jurisdiction of observation, and if so, the nature of the inter-jurisdictional relationship.
   *
   *
   *                            OpenIssue: This code could be moved to LOINC if it can be done before there are significant implemenations using it.
   */
  CaseDiseaseImportedObservation: v3_ActCode_CaseDiseaseImportedObservation,
  /**
   * Description: The class room associated with the patient during the immunization event.
   */
  Classroom: v3_ActCode_Classroom,
  /**
   * Indicates what method is used in a quality measure to combine the component measure results included in an composite measure.
   */
  CompositeMeasureMethod: v3_ActCode_CompositeMeasureMethod,
  /**
   * An attribute of a quality measure describing the weight this component measure score is to carry in determining the overall composite measure final score. The value is real value greater than 0 and less than 1.0. Each component measure score will be multiplied by its CMPMSRSCRWGHT and then summed with the other component measures to determine the final overall composite measure score. The sum across all CMPMSRSCRWGHT values within a single composite measure SHALL be 1.0. The value assigned is scoped to the composite measure referencing this component measure only.
   */
  ComponentMeasureScoringWeight: v3_ActCode_ComponentMeasureScoringWeight,
  /**
   * Description:The specified code has been deprecated and should no longer be used.  Select another code from the code system.
   */
  CodeHasBeenDeprecated: v3_ActCode_CodeHasBeenDeprecated,
  /**
   * Description:The specified code is not valid against the list of codes allowed for the element.
   */
  CodeIsNotValid: v3_ActCode_CodeIsNotValid,
  /**
   * There may be an issue with the patient complying with the intentions of the proposed therapy
   */
  ComplianceAlert: v3_ActCode_ComplianceAlert,
  /**
   * Proposed therapy may be inappropriate or contraindicated due to an existing/recent patient condition or diagnosis
   */
  ConditionAlert: v3_ActCode_ConditionAlert,
  /**
   * The quantity of specimen that is used each time the equipment uses this substance
   */
  ConsumptionVolume: v3_ActCode_ConsumptionVolume,
  /**
   * Identifies the organization(s) who own the intellectual property represented by the eMeasure.
   */
  Copyright: v3_ActCode_Copyright,
  /**
   * Description:Proposed therapy may be inappropriate or contraindicated because of a common but non-patient specific reaction to the product.
   *
   *
   *                            Example:There is no record of a specific sensitivity for the patient, but the presence of the sensitivity is common and therefore caution is warranted.
   */
  CommonReactionAlert: v3_ActCode_CommonReactionAlert,
  /**
   * A clinical judgment as to the worst case result of a future exposure (including substance administration). When the worst case result is assessed to have a life-threatening or organ system threatening potential, it is considered to be of high criticality.
   */
  Criticality: v3_ActCode_Criticality,
  /**
   * Summary of relevant clinical guidelines or other clinical recommendations supporting this eMeasure.
   */
  ClinicalRecommendationStatement: v3_ActCode_ClinicalRecommendationStatement,
  /**
   * An observation that states the mechanism by which disease was acquired by the living subject involved in the public health case.
   *
   *
   *                            OpenIssue: This code could be moved to LOINC if it can be done before there are significant implemenations using it.
   */
  CaseTransmissionModeObservation: v3_ActCode_CaseTransmissionModeObservation,
  /**
   * The current quantity of the specimen, i.e., initial quantity minus what has been actually used.
   */
  CurrentVolume: v3_ActCode_CurrentVolume,
  /**
   * Description:Proposed therapy may be contraindicated or ineffective based on an existing or recent drug therapy.
   */
  DrugActionDetectedIssue: v3_ActCode_DrugActionDetectedIssue,
  /**
   * An allergy to a pharmaceutical product.
   */
  DrugAllergy: v3_ActCode_DrugAllergy,
  /**
   * Description of individual terms, provided as needed.
   */
  Definition: v3_ActCode_Definition,
  /**
   * Criteria which specify subjects who should be removed from the eMeasure population and denominator before determining if numerator criteria are met. Denominator exclusions are used in proportion and ratio measures to help narrow the denominator.
   */
  DenominatorExclusions: v3_ActCode_DenominatorExclusions,
  /**
   * Criteria which specify the removal of a subject, procedure or unit of measurement from the denominator, only if the numerator criteria are not met. Denominator exceptions allow for adjustment of the calculated score for those providers with higher risk populations. Denominator exceptions are used only in proportion eMeasures. They are not appropriate for ratio or continuous variable eMeasures. Denominator exceptions allow for the exercise of clinical judgment and should be specifically defined where capturing the information in a structured manner fits the clinical workflow. Generic denominator exception reasons used in proportion eMeasures fall into three general categories:
   *
   *
   *                            Medical reasons
   *                            Patient (or subject) reasons
   *                            System reasons
   */
  DenominatorExceptions: v3_ActCode_DenominatorExceptions,
  /**
   * Criteria for specifying the entities to be evaluated by a specific quality measure, based on a shared common set of characteristics (within a specific measurement set to which a given measure belongs).  The denominator can be the same as the initial population, or it may be a subset of the initial population to further constrain it for the purpose of the eMeasure. Different measures within an eMeasure set may have different denominators. Continuous Variable eMeasures do not have a denominator, but instead define a measure population.
   */
  Denominator: v3_ActCode_Denominator,
  /**
   * Communication of an agent from a living subject or environmental source to a living subject via agent migration through intact skin.
   */
  TransdermalTransmission: v3_ActCode_TransdermalTransmission,
  /**
   * An observation that reports the dilution of a sample.
   */
  ActSpecObsDilutionCode: v3_ActCode_ActSpecObsDilutionCode,
  /**
   * Hypersensitivity resulting in an adverse reaction upon exposure to a drug.
   */
  DrugIntolerance: v3_ActCode_DrugIntolerance,
  /**
   * Disclaimer information for the eMeasure.
   */
  Disclaimer: v3_ActCode_Disclaimer,
  /**
   * Discharge diagnosis are the diagnoses documented for administrative purposes as the time of hospital discharge.
   */
  DischargeDiagnosis: v3_ActCode_DischargeDiagnosis,
  /**
   * Hypersensitivity to an agent caused by a mechanism other than an immunologic response to an initial exposure
   */
  DrugNonAllergyIntolerance: v3_ActCode_DrugNonAllergyIntolerance,
  /**
   * Proposed dosage instructions for therapy differ from standard practice.
   */
  DosageProblem: v3_ActCode_DosageProblem,
  /**
   * Description:Proposed dosage is inappropriate due to patient's medical condition.
   */
  DosageConditionAlert: v3_ActCode_DosageConditionAlert,
  /**
   * Proposed length of therapy differs from standard practice.
   */
  DoseDurationAlert: v3_ActCode_DoseDurationAlert,
  /**
   * Proposed length of therapy is longer than standard practice
   */
  DoseDurationHighAlert: v3_ActCode_DoseDurationHighAlert,
  /**
   * Proposed length of therapy is longer than standard practice for the identified indication or diagnosis
   */
  DoseDurationHighForIndicationAlert: v3_ActCode_DoseDurationHighForIndicationAlert,
  /**
   * Proposed length of therapy is shorter than that necessary for therapeutic effect
   */
  DoseDurationLowAlert: v3_ActCode_DoseDurationLowAlert,
  /**
   * Proposed length of therapy is shorter than standard practice for the identified indication or diagnosis
   */
  DoseDurationLowForIndicationAlert: v3_ActCode_DoseDurationLowForIndicationAlert,
  /**
   * Proposed dosage exceeds standard practice
   */
  HighDoseAlert: v3_ActCode_HighDoseAlert,
  /**
   * High Dose for Indication Alert
   */
  HighDoseForIndicationAlert: v3_ActCode_HighDoseForIndicationAlert,
  /**
   * Proposed dosage exceeds standard practice for the patient's age
   */
  HighDoseForAgeAlert: v3_ActCode_HighDoseForAgeAlert,
  /**
   * Proposed dosage exceeds standard practice for the patient's height or body surface area
   */
  HighDoseForHeightSurfaceAreaAlert: v3_ActCode_HighDoseForHeightSurfaceAreaAlert,
  /**
   * Proposed dosage exceeds standard practice for the patient's weight
   */
  HighDoseForWeightAlert: v3_ActCode_HighDoseForWeightAlert,
  /**
   * Proposed dosage interval/timing differs from standard practice
   */
  DoseIntervalAlert: v3_ActCode_DoseIntervalAlert,
  /**
   * Proposed dosage interval/timing differs from standard practice for the identified indication or diagnosis
   */
  DoseIntervalForIndicationAlert: v3_ActCode_DoseIntervalForIndicationAlert,
  /**
   * Proposed dosage is below suggested therapeutic levels
   */
  LowDoseAlert: v3_ActCode_LowDoseAlert,
  /**
   * Low Dose for Indication Alert
   */
  LowDoseForIndicationAlert: v3_ActCode_LowDoseForIndicationAlert,
  /**
   * Proposed dosage is below suggested therapeutic levels for the patient's age
   */
  LowDoseForAgeAlert: v3_ActCode_LowDoseForAgeAlert,
  /**
   * Proposed dosage is below suggested therapeutic levels for the patient's height or body surface area
   */
  LowDoseForHeightSurfaceAreaAlert: v3_ActCode_LowDoseForHeightSurfaceAreaAlert,
  /**
   * Proposed dosage is below suggested therapeutic levels for the patient's weight
   */
  LowDoseForWeightAlert: v3_ActCode_LowDoseForWeightAlert,
  /**
   * Proposed therapy may interact with an existing or recent drug therapy
   */
  DrugInteractionAlert: v3_ActCode_DrugInteractionAlert,
  /**
   * Description:The proposed therapy appears to have the same intended therapeutic benefit as an existing therapy, though the specific mechanisms of action vary.
   */
  DuplicateTherapeuticAlassAlert: v3_ActCode_DuplicateTherapeuticAlassAlert,
  /**
   * Description:The proposed therapy appears to have the same intended therapeutic benefit as an existing therapy and uses the same mechanisms of action as the existing therapy.
   */
  DuplicateGenericAlert: v3_ActCode_DuplicateGenericAlert,
  /**
   * The proposed therapy appears to duplicate an existing therapy
   */
  DuplicateTherapyAlert: v3_ActCode_DuplicateTherapyAlert,
  /**
   * Includes all codes defining types of indications such as diagnosis, symptom and other indications such as contrast agents for lab tests.
   */
  ObservationDiagnosisTypes: v3_ActCode_ObservationDiagnosisTypes,
  /**
   * An allergy to a substance other than a drug or a food.  E.g. Latex, pollen, etc.
   */
  EnvironmentalAllergy: v3_ActCode_EnvironmentalAllergy,
  /**
   * Hypersensitivity resulting in an adverse reaction upon exposure to environmental conditions.
   */
  EnvironmentalIntolerance: v3_ActCode_EnvironmentalIntolerance,
  /**
   * Hypersensitivity to an agent caused by a mechanism other than an immunologic response to an initial exposure
   */
  EnvironmentalNonAllergyIntolerance: v3_ActCode_EnvironmentalNonAllergyIntolerance,
  /**
   * Proposed therapy may be inappropriate or ineffective because the end of administration is too close to another planned therapy
   */
  EndTooLateAlert_2: v3_ActCode_EndTooLateAlert_2,
  /**
   * Communication of an agent from an environmental surface or source to a living subject by direct contact.
   */
  EnvironmentalExposureTransmission: v3_ActCode_EnvironmentalExposureTransmission,
  /**
   * Domain provides codes that qualify the ActLabObsEnvfctsCode domain. (Environmental Factors)
   */
  ActSpecObsEvntfctsCode: v3_ActCode_ActSpecObsEvntfctsCode,
  /**
   * An allergy to a substance generally consumed for nutritional purposes.
   */
  FoodAllergy: v3_ActCode_FoodAllergy,
  /**
   * FDA label coating
   */
  Coating: v3_ActCode_Coating,
  /**
   * FDA label color
   */
  Color: v3_ActCode_Color,
  /**
   * FDA label imprint code
   */
  ImprintCode: v3_ActCode_ImprintCode,
  /**
   * FDA label logo
   */
  Logo: v3_ActCode_Logo,
  /**
   * FDA label scoring
   */
  Scoring: v3_ActCode_Scoring,
  /**
   * FDA label shape
   */
  Shape: v3_ActCode_Shape,
  /**
   * FDA label size
   */
  Size: v3_ActCode_Size,
  /**
   * Communication of an agent from a living subject or environmental source to a living subject through oral contact with material contaminated by person or animal fecal material.
   */
  FecalOralTransmission: v3_ActCode_FecalOralTransmission,
  /**
   * The Fibrin Index of the specimen. In the case of only differentiating between Absent and Present, recommend using 0 and 1
   */
  Fibrin: v3_ActCode_Fibrin,
  /**
   * The timestamp when the eMeasure was last packaged in the Measure Authoring Tool.
   */
  FinalizedDateTime: v3_ActCode_FinalizedDateTime,
  /**
   * Hypersensitivity resulting in an adverse reaction upon exposure to food.
   */
  FoodIntolerance: v3_ActCode_FoodIntolerance,
  /**
   * Hypersensitivity to an agent caused by a mechanism other than an immunologic response to an initial exposure
   */
  FoodNonAllergyIntolerance: v3_ActCode_FoodNonAllergyIntolerance,
  /**
   * Communication of an agent from an non-living material to a living subject through direct contact.
   */
  FomiteTransmission: v3_ActCode_FomiteTransmission,
  /**
   * Proposed therapy may interact with certain foods
   */
  FoodInteractionAlert: v3_ActCode_FoodInteractionAlert,
  /**
   * Communication of an agent from a food source to a living subject via oral consumption.
   */
  FoodBorneTransmission: v3_ActCode_FoodBorneTransmission,
  /**
   * Description:The element does not follow the formatting or type rules defined for the field.
   */
  InvalidFormat: v3_ActCode_InvalidFormat,
  /**
   * Description:The request is suspected to have a fraudulent basis.
   */
  PotentialFraud: v3_ActCode_PotentialFraud,
  /**
   * Definition:The therapy being performed is in some way out of alignment with the requested therapy.
   */
  FulfillmentAlert: v3_ActCode_FulfillmentAlert,
  /**
   * Proposed therapy is outside of standard practice for a geriatric patient.
   */
  GeriatricAlert: v3_ActCode_GeriatricAlert,
  /**
   * Proposed therapy may be inappropriate or contraindicated due to patient genetic indicators.
   */
  GeneticAlert: v3_ActCode_GeneticAlert,
  /**
   * Proposed therapy may be inappropriate or contraindicated due to patient gender.
   */
  GenderAlert: v3_ActCode_GenderAlert,
  /**
   * Description: A DNA segment that contributes to phenotype/function. In the absence of demonstrated function a gene may be characterized by sequence, transcription or homology
   */
  Gene: v3_ActCode_Gene,
  /**
   * Description: Accuracy determined as per the GIS tier code system.
   */
  GISTier: v3_ActCode_GISTier,
  /**
   * Description: The school grade or level the patient was in when immunized.
   */
  Grade: v3_ActCode_Grade,
  /**
   * Used to allow measure developers to provide additional guidance for implementers to understand greater specificity than could be provided in the logic for data criteria.
   */
  Guidance: v3_ActCode_Guidance,
  /**
   * Definition:There should be no actions taken in fulfillment of a request that has been held or suspended.
   */
  HeldSuspendedAlert: v3_ActCode_HeldSuspendedAlert,
  /**
   * An observation of the hemolysis index of the specimen in g/L
   */
  Hemolysis: v3_ActCode_Hemolysis,
  HGHT: v3_ActCode_HGHT,
  /**
   * Indicates that the observation is of a person’s living situation in a household including the household composition and circumstances.
   */
  HouseholdSituationObservation: v3_ActCode_HouseholdSituationObservation,
  /**
   * Description: While the record was accepted in the repository, there is a more recent version of a record of this type.
   */
  RecordRecordedAsHistorical: v3_ActCode_RecordRecordedAsHistorical,
  /**
   * Communication of an agent from a person to a proximate person.
   */
  HumanToHumanTransmission: v3_ActCode_HumanToHumanTransmission,
  /**
   * An observation that describes the icterus index of the specimen.  It is recommended to use mMol/L of bilirubin
   */
  Icterus: v3_ActCode_Icterus,
  /**
   * Information on whether an increase or decrease in score is the preferred result
   * (e.g., a higher score indicates better quality OR a lower score indicates better quality OR quality is within a range).
   */
  ImprovementNotation: v3_ActCode_ImprovementNotation,
  /**
   * Description:The request is missing elements or contains elements which cause it to not meet the legal standards for actioning.
   */
  Illegal: v3_ActCode_Illegal,
  /**
   * Communication of an agent to a living subject via an undetermined route.
   */
  IndeterminateDiseaseTransmissionMode: v3_ActCode_IndeterminateDiseaseTransmissionMode,
  /**
   * The initial quantity of the specimen in inventory
   */
  InitialVolume: v3_ActCode_InitialVolume,
  /**
   * Proposed therapy may be inappropriate or contraindicated because of a recorded patient intolerance to the proposed product.  (Intolerances are non-immune based sensitivities.)
   */
  IntoleranceAlert: v3_ActCode_IntoleranceAlert,
  /**
   * Intermediate diagnoses are those diagnoses documented for administrative purposes during the course of a hospital stay.
   */
  IntermediateDiagnosis: v3_ActCode_IntermediateDiagnosis,
  /**
   * Definition:The therapy action is being performed outside the bounds of the time period requested
   */
  OutsideRequestedTime: v3_ActCode_OutsideRequestedTime,
  /**
   * An observation that relates to factors that may potentially cause interference with the observation
   */
  ActSpecObsInterferenceCode: v3_ActCode_ActSpecObsInterferenceCode,
  /**
   * Criteria for specifying the entities to be evaluated by a specific quality measure, based on a shared common set of characteristics (within a specific measurement set to which a given measure belongs).
   */
  InitialPopulation: v3_ActCode_InitialPopulation,
  /**
   * Criteria for specifying the patients to be evaluated by a specific quality measure, based on a shared common set of characteristics (within a specific measurement set to which a given measure belongs). Details often include information based upon specific age groups, diagnoses, diagnostic and procedure codes, and enrollment periods.
   */
  InitialPatientPopulation: v3_ActCode_InitialPatientPopulation,
  /**
   * There is a clinical issue for the therapy that makes continuation of the therapy inappropriate.
   *
   *
   *                            Open Issue: The definition of this code does not correctly represent the concept space of its specializations (children)
   */
  DetectedIssue: v3_ActCode_DetectedIssue,
  /**
   * Describes the items counted by the measure (e.g., patients, encounters, procedures, etc.)
   */
  ItemsCounted: v3_ActCode_ItemsCounted,
  /**
   * A significant word that aids in discoverability.
   */
  Keyword: v3_ActCode_Keyword,
  /**
   * The ID of the patient, order, etc., was not found. Used for transactions other than additions, e.g. transfer of a non-existent patient.
   */
  UnknownKeyIdentifier: v3_ActCode_UnknownKeyIdentifier,
  /**
   * The ID of the patient, order, etc., already exists. Used in response to addition transactions (Admit, New Order, etc.).
   */
  DuplicateKeyIdentifier: v3_ActCode_DuplicateKeyIdentifier,
  /**
   * Description: Metadata associated with the identification (e.g. name or gender) does not match the identification being verified.
   */
  NonMatchingIdentification: v3_ActCode_NonMatchingIdentification,
  /**
   * Categorization of types of observation that capture the main clinical knowledge subject which may be a medication, a laboratory test, a disease.
   */
  KnowledgeSubject: v3_ActCode_KnowledgeSubject,
  /**
   * Categorization of types of observation that capture a knowledge subtopic which might be treatment, etiology, or prognosis.
   */
  KnowledgeSubtopic: v3_ActCode_KnowledgeSubtopic,
  /**
   * Proposed therapy may be inappropriate or contraindicated due to recent lab test results
   */
  LabAlert: v3_ActCode_LabAlert,
  /**
   * Proposed therapy may be inappropriate or contraindicated when breast-feeding
   */
  LactationAlert: v3_ActCode_LactationAlert,
  /**
   * Communication of an agent from one living subject to another living subject through direct contact with mammalian milk or colostrum.
   */
  LactationTransmission: v3_ActCode_LactationTransmission,
  /**
   * Description:The length of the data specified is greater than the maximum length defined for the element.
   */
  LengthIsTooLong: v3_ActCode_LengthIsTooLong,
  /**
   * Description:The length of the data specified falls out of the range defined for the element.
   */
  LengthOutOfRange: v3_ActCode_LengthOutOfRange,
  /**
   * Description:The length of the data specified is less than the minimum length defined for the element.
   */
  LengthIsTooShort: v3_ActCode_LengthIsTooShort,
  /**
   * An observation used to describe the Lipemia Index of the specimen. It is recommended to use the optical turbidity at 600 nm (in absorbance units).
   */
  Lipemia: v3_ActCode_Lipemia,
  /**
   * Description:The number of repeating elements is above the maximum number of repetitions allowed.
   */
  RepetitionsAboveMaximum: v3_ActCode_RepetitionsAboveMaximum,
  /**
   * Description:The maximum quantity of this drug allowed to be administered within a particular time-range (month, year, lifetime) has been reached or exceeded.
   */
  MaximumDosageReached: v3_ActCode_MaximumDosageReached,
  /**
   * The end date of the measurement period.
   */
  MeasurementEndDate: v3_ActCode_MeasurementEndDate,
  /**
   * Definition:The therapy action is being performed too soon after the previous occurrence based on the requested frequency
   */
  TooSoonWithinFrequencyBasedOnTheUsage: v3_ActCode_TooSoonWithinFrequencyBasedOnTheUsage,
  /**
   * Description:The number of repeating elements is below the minimum number of repetitions allowed.
   */
  RepetitionsBelowMinimum: v3_ActCode_RepetitionsBelowMinimum,
  /**
   * Description:The specified element must be specified with a non-null value under certain conditions.  In this case, the conditions are true but the element is still missing or null.
   */
  ConditionalElementMissing: v3_ActCode_ConditionalElementMissing,
  /**
   * Description:The specified element is mandatory and was not included in the instance.
   */
  MandatoryElementMissing: v3_ActCode_MandatoryElementMissing,
  /**
   * The start date of the measurement period.
   */
  MeasurementStartDate: v3_ActCode_MeasurementStartDate,
  /**
   * The method of adjusting for clinical severity and conditions present at the start of care that can influence patient outcomes for making valid comparisons of outcome measures across providers. Indicates whether an eMeasure is subject to the statistical process for reducing, removing, or clarifying the influences of confounding factors to allow more useful comparisons.
   */
  RiskAdjustment: v3_ActCode_RiskAdjustment,
  /**
   * Describes how to combine information calculated based on logic in each of several populations into one summarized result. It can also be used to describe how to risk adjust the data based on supplemental data elements described in the eMeasure. (e.g., pneumonia hospital measures antibiotic selection in the ICU versus non-ICU and then the roll-up of the two).
   *
   *
   *                            Open Issue: The description does NOT align well with the definition used in the HQMF specfication; correct the MSGAGG definition, and the possible distinction of MSRAGG as a child of AGGREGATE.
   */
  RateAggregation: v3_ActCode_RateAggregation,
  /**
   * Information on whether an increase or decrease in score is the preferred result. This should reflect information on which way is better, an increase or decrease in score.
   */
  HealthQualityMeasureImprovementNotation: v3_ActCode_HealthQualityMeasureImprovementNotation,
  /**
   * The list of jurisdiction(s) for which the measure applies.
   */
  Jurisdiction: v3_ActCode_Jurisdiction,
  /**
   * Defines the observation to be performed for each patient or event in the measure population. Measure observations for each case in the population are aggregated to determine the overall measure score for the population.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            the median time from arrival in the Emergency Room to departure
   *                            the median time from decision to admit to a hospital to the actual admission for Emergency Room patients
   */
  MeasureObservation: v3_ActCode_MeasureObservation,
  /**
   * Criteria for specifying
   * the measure population as a narrative description (e.g., all patients seen in the Emergency Department during the measurement period).  This is used only in continuous variable eMeasures.
   */
  MeasurePopulation: v3_ActCode_MeasurePopulation,
  /**
   * Criteria for specifying subjects who should be removed from the eMeasure's Initial Population and Measure Population. Measure Population Exclusions are used in Continuous Variable measures to help narrow the Measure Population before determining the value(s) of the continuous variable(s).
   */
  MeasurePopulationExclusions: v3_ActCode_MeasurePopulationExclusions,
  /**
   * Type of person or organization that is expected to report the issue.
   */
  ReporterType: v3_ActCode_ReporterType,
  /**
   * The maximum time that may elapse following completion of the measure until the measure report must be sent to the receiver.
   */
  TimeframeForReporting: v3_ActCode_TimeframeForReporting,
  /**
   * Indicates how the calculation is performed for the eMeasure
   * (e.g., proportion, continuous variable, ratio)
   */
  MeasureScoring: v3_ActCode_MeasureScoring,
  /**
   * Location(s) in which care being measured is rendered
   *
   *                         Usage Note: MSRSET is used rather than RoleCode because the setting applies to what is being measured, as opposed to participating directly in the health quality measure documantion itself).
   */
  HealthQualityMeasureCareSetting: v3_ActCode_HealthQualityMeasureCareSetting,
  /**
   * health quality measure topic type
   */
  HealthQualityMeasureTopicType: v3_ActCode_HealthQualityMeasureTopicType,
  /**
   * The time period for which the eMeasure applies.
   */
  MeasurementPeriod: v3_ActCode_MeasurementPeriod,
  /**
   * Indicates whether the eMeasure is used to examine a process or an outcome over time
   * (e.g., Structure, Process, Outcome).
   */
  MeasureType: v3_ActCode_MeasureType,
  /**
   * Hypersensitivity to an agent caused by a mechanism other than an immunologic response to an initial exposure
   */
  NonAllergyIntolerance: v3_ActCode_NonAllergyIntolerance,
  /**
   * The requesting party has insufficient authorization to invoke the interaction.
   */
  InsufficientAuthorization: v3_ActCode_InsufficientAuthorization,
  /**
   * Proposed therapy may interact with existing or recent natural health product therapy
   */
  NaturalHealthProductAlert: v3_ActCode_NaturalHealthProductAlert,
  /**
   * Description:More than one element with the same value exists in the set.  Duplicates not permission in this set in a set.
   */
  DuplicateValuesAreNotPermitted: v3_ActCode_DuplicateValuesAreNotPermitted,
  /**
   * The type of injury that the injury coding specifies.
   */
  NatureOfInjury: v3_ActCode_NatureOfInjury,
  /**
   * Proposed therapy may interact with a non-prescription drug (e.g. alcohol, tobacco, Aspirin)
   */
  NonPrescriptionInteractionAlert: v3_ActCode_NonPrescriptionInteractionAlert,
  /**
   * Description: Element in submitted message will not persist in data storage based on detected issue.
   */
  ElementWillNotBePersisted: v3_ActCode_ElementWillNotBePersisted,
  /**
   * Communication of an agent from any entity to a living subject while the living subject is in the patient role in a healthcare facility.
   */
  NosocomialTransmission: v3_ActCode_NosocomialTransmission,
  /**
   * Definition:The status of the request being fulfilled has changed such that it is no longer actionable.  This may be because the request has expired, has already been completely fulfilled or has been otherwise stopped or disabled.  (Not used for 'suspended' orders.)
   */
  NoLongerActionable: v3_ActCode_NoLongerActionable,
  /**
   * Definition:The therapy being performed is not sufficiently equivalent to the therapy which was requested.
   */
  NotEquivalentAlert: v3_ActCode_NotEquivalentAlert,
  /**
   * Definition:The therapy being performed is not generically equivalent (having the identical biological action) to the therapy which was requested.
   */
  NotGenericallyEquivalentAlert: v3_ActCode_NotGenericallyEquivalentAlert,
  /**
   * Definition:The therapy being performed is not therapeutically equivalent (having the same overall patient effect) to the therapy which was requested.
   */
  NotTherapeuticallyEquivalentAlert: v3_ActCode_NotTherapeuticallyEquivalentAlert,
  /**
   * Criteria for specifying the processes or outcomes expected for each patient, procedure, or other unit of measurement defined in the denominator for proportion measures, or related to (but not directly derived from) the denominator for ratio measures (e.g., a numerator listing the number of central line blood stream infections and a denominator indicating the days per thousand of central line usage in a specific time period).
   */
  Numerator: v3_ActCode_Numerator,
  /**
   * Criteria for specifying instances that should not be included in the numerator data. (e.g., if the number of central line blood stream infections per 1000 catheter days were to exclude infections with a specific bacterium, that bacterium would be listed as a numerator exclusion).  Numerator Exclusions are used only in ratio eMeasures.
   */
  NumeratorExclusions: v3_ActCode_NumeratorExclusions,
  /**
   * Proposed therapy may be inappropriate or contraindicated due to conditions or characteristics of the patient
   */
  ObservationAlert: v3_ActCode_ObservationAlert,
  /**
   * Description: Indicates the valid antigen count.
   */
  AntigenCount: v3_ActCode_AntigenCount,
  /**
   * Description: Indicates whether an antigen is valid or invalid.
   */
  AntigenValidity: v3_ActCode_AntigenValidity,
  /**
   * Description: One or more records in the query response have a status of 'obsolete'.
   */
  ObsoleteRecordReturned: v3_ActCode_ObsoleteRecordReturned,
  /**
   * Hypersensitivity resulting in an adverse reaction upon exposure to an agent.
   */
  Intolerance: v3_ActCode_Intolerance,
  /**
   * Communication of an agent from a living subject or environmental source to a living subject where the acquisition of the agent is not via the alimentary canal.
   */
  ParenteralTransmission: v3_ActCode_ParenteralTransmission,
  /**
   * Indicates that the ICSR is describing problems that a patient experienced after receiving a vaccine product.
   */
  PatientAdverseEvent: v3_ActCode_PatientAdverseEvent,
  /**
   * Definition:The proposed therapy goes against preferences or consent constraints recorded in the patient's record.
   */
  ViolatesStatedPreferences: v3_ActCode_ViolatesStatedPreferences,
  /**
   * Definition:The proposed therapy goes against preferences or consent constraints recorded in the patient's record.  An alternate therapy meeting those constraints is available.
   */
  ViolatesStatedPreferencesAlternateAvailable: v3_ActCode_ViolatesStatedPreferencesAlternateAvailable,
  /**
   * Proposed therapy is outside of the standard practice for a pediatric patient.
   */
  PediatricAlert: v3_ActCode_PediatricAlert,
  /**
   * Communication of an agent from a living subject to the progeny of that living subject via agent migration across the maternal-fetal placental membranes while in utero.
   */
  TransplacentalTransmission: v3_ActCode_TransplacentalTransmission,
  /**
   * A similar or identical therapy was recently ordered by a different practitioner.
   */
  PolyOrdererAlert: v3_ActCode_PolyOrdererAlert,
  /**
   * This patient was recently supplied a similar or identical therapy from a different pharmacy or supplier.
   */
  PolySupplierAlert: v3_ActCode_PolySupplierAlert,
  /**
   * The dilution of the specimen made prior to being loaded onto analytical equipment
   */
  PreDilution: v3_ActCode_PreDilution,
  /**
   * An observation about how important a preference is to the target of the preference.
   */
  PreferenceStrength: v3_ActCode_PreferenceStrength,
  /**
   * Proposed therapy may be inappropriate or contraindicated during pregnancy
   */
  PregnancyAlert: v3_ActCode_PregnancyAlert,
  /**
   * Definition:The same or similar treatment has previously been attempted with the patient without achieving a positive effect.
   */
  PreviouslyIneffective: v3_ActCode_PreviouslyIneffective,
  /**
   * Proposed therapy may be inappropriate or contraindicated because of a recorded patient allergy to a cross-sensitivity related product.  (Allergies are immune based reactions.)
   */
  RelatedAllergyAlert: v3_ActCode_RelatedAllergyAlert,
  /**
   * Proposed therapy may be inappropriate or contraindicated because of a recorded prior adverse reaction to a cross-sensitivity related product.
   */
  RelatedPriorReactionAlert: v3_ActCode_RelatedPriorReactionAlert,
  /**
   * Succinct statement of the need for the measure. Usually includes statements pertaining to Importance criterion: impact, gap in care and evidence.
   */
  Rationale: v3_ActCode_Rationale,
  /**
   * Proposed therapy may be inappropriate or contraindicated based on the potential for a patient reaction to the proposed product
   */
  ReactionAlert: v3_ActCode_ReactionAlert,
  /**
   * Identifies bibliographic citations or references to clinical practice guidelines, sources of evidence, or other relevant materials supporting the intent and rationale of the eMeasure.
   */
  Reference: v3_ActCode_Reference,
  /**
   * Description:This observation represents an 'average' or 'expected' half-life typical of the product.
   */
  RepresentativeHalfLife: v3_ActCode_RepresentativeHalfLife,
  /**
   * Description:The number of repeating elements falls outside the range of the allowed number of repetitions.
   */
  RepetitionsOutOfRange: v3_ActCode_RepetitionsOutOfRange,
  /**
   * This Observation Series type contains waveforms of a "representative beat" (a.k.a. "median beat" or "average beat").  The waveform samples are measured in relative time, relative to the beginning of the beat as defined by the Observation Series effective time.  The waveforms are not directly acquired from the subject, but rather algorithmically derived from the "rhythm" waveforms.
   */
  ECGRepresentativeBeatWaveforms: v3_ActCode_ECGRepresentativeBeatWaveforms,
  /**
   * The value of the dilution of a sample after it had been analyzed at a prior dilution value
   */
  RerunDilution: v3_ActCode_RerunDilution,
  /**
   * This Observation type contains ECG "rhythm" waveforms.  The waveform samples are measured in absolute time (a.k.a. "subject time" or "effective time").  These waveforms are usually "raw" with some minimal amount of noise reduction and baseline filtering applied.
   */
  ECGRhythmWaveforms: v3_ActCode_ECGRhythmWaveforms,
  /**
   * Proposed therapy may be inappropriate or contraindicated because of a recorded patient intolerance to a cross-sensitivity related product.  (Intolerances are non-immune based sensitivities.)
   */
  RelatedIntoleranceAlert: v3_ActCode_RelatedIntoleranceAlert,
  /**
   * Proposed therapy may be inappropriate or contraindicated because of a potential patient reaction to a cross-sensitivity related product.
   */
  RelatedReactionAlert: v3_ActCode_RelatedReactionAlert,
  /**
   * Description: The school the patient attended when immunized.
   */
  School: v3_ActCode_School,
  /**
   * Description: The school division or district associated with the patient during the immunization event.
   */
  SchoolDivision: v3_ActCode_SchoolDivision,
  /**
   * Comparison of results across strata can be used to show where disparities exist or where there is a need to expose differences in results. For example, Centers for Medicare & Medicaid Services (CMS) in the U.S. defines four required Supplemental Data Elements (payer, ethnicity, race, and gender), which are variables used to aggregate data into various subgroups. Additional supplemental data elements required for risk adjustment or other purposes of data aggregation can be included in the Supplemental Data Element section.
   */
  SupplementalDataElements: v3_ActCode_SupplementalDataElements,
  /**
   * A subjective evaluation of the seriousness or intensity associated with another observation.
   */
  SeverityObservation: v3_ActCode_SeverityObservation,
  /**
   * Communication of an agent from one living subject to another living subject through direct contact with genital or oral tissues as part of a sexual act.
   */
  SexualTransmission: v3_ActCode_SexualTransmission,
  /**
   * Definition: A characteristic of an oral solid dosage form of a medicinal product, indicating whether it has one or more coatings such as sugar coating, film coating, or enteric coating.  Only coatings to the external surface or the dosage form should be considered (for example, coatings to individual pellets or granules inside a capsule or tablet are excluded from consideration).
   *
   *
   *                            Constraints: The Observation.value must be a Boolean (BL) with true for the presence or false for the absence of one or more coatings on a solid dosage form.
   */
  Coating_2: v3_ActCode_Coating_2,
  /**
   * Definition:  A characteristic of an oral solid dosage form of a medicinal product, specifying the color or colors that most predominantly define the appearance of the dose form. SPLCOLOR is not an FDA specification for the actual color of solid dosage forms or the names of colors that can appear in labeling.
   *
   *
   *                            Constraints: The Observation.value must be a single coded value or a list of multiple coded values, specifying one or more distinct colors that approximate of the color(s) of distinct areas of the solid dosage form, such as the different sides of a tablet or one-part capsule, or the different halves of a two-part capsule.  Bands on banded capsules, regardless of the color, are not considered when assigning an SPLCOLOR. Imprints on the dosage form, regardless of their color are not considered when assigning an SPLCOLOR. If more than one color exists on a particular side or half, then the most predominant color on that side or half is recorded.  If the gelatin capsule shell is colorless and transparent, use the predominant color of the contents that appears through the colorless and transparent capsule shell. Colors can include: Black;Gray;White;Red;Pink;Purple;Green;Yellow;Orange;Brown;Blue;Turquoise.
   */
  Color_2: v3_ActCode_Color_2,
  /**
   * Description: A characteristic representing a single file reference that contains two or more views of the same dosage form of the product; in most cases this should represent front and back views of the dosage form, but occasionally additional views might be needed in order to capture all of the important physical characteristics of the dosage form.  Any imprint and/or symbol should be clearly identifiable, and the viewer should not normally need to rotate the image in order to read it.  Images that are submitted with SPL should be included in the same directory as the SPL file.
   */
  Image: v3_ActCode_Image,
  /**
   * Definition:  A characteristic of an oral solid dosage form of a medicinal product, specifying the alphanumeric text that appears on the solid dosage form, including text that is embossed, debossed, engraved or printed with ink. The presence of other non-textual distinguishing marks or symbols is recorded by SPLSYMBOL.
   *
   *
   *                            Examples: Included in SPLIMPRINT are alphanumeric text that appears on the bands of banded capsules and logos and other symbols that can be interpreted as letters or numbers.
   *
   *
   *                            Constraints: The Observation.value must be of type Character String (ST). Excluded from SPLIMPRINT are internal and external cut-outs in the form of alphanumeric text and the letter 'R' with a circle around it (when referring to a registered trademark) and the letters 'TM' (when referring to a 'trade mark').  To record text, begin on either side or part of the dosage form. Start at the top left and progress as one would normally read a book.  Enter a semicolon to show separation between words or line divisions.
   */
  Imprint: v3_ActCode_Imprint,
  /**
   * Definition: A characteristic of an oral solid dosage form of a medicinal product, specifying the number of equal pieces that the solid dosage form can be divided into using score line(s).
   *
   *
   *                            Example: One score line creating two equal pieces is given a value of 2, two parallel score lines creating three equal pieces is given a value of 3.
   *
   *
   *                            Constraints: Whether three parallel score lines create four equal pieces or two intersecting score lines create two equal pieces using one score line and four equal pieces using both score lines, both have the scoring value of 4. Solid dosage forms that are not scored are given a value of 1. Solid dosage forms that can only be divided into unequal pieces are given a null-value with nullFlavor other (OTH).
   */
  Scoring_2: v3_ActCode_Scoring_2,
  /**
   * Description: A characteristic of an oral solid dosage form of a medicinal product, specifying the two dimensional representation of the solid dose form, in terms of the outside perimeter of a solid dosage form when the dosage form, resting on a flat surface, is viewed from directly above, including slight rounding of corners. SPLSHAPE does not include embossing, scoring, debossing, or internal cut-outs.  SPLSHAPE is independent of the orientation of the imprint and logo. Shapes can include: Triangle (3 sided); Square; Round; Semicircle; Pentagon (5 sided); Diamond; Double circle; Bullet; Hexagon (6 sided); Rectangle; Gear; Capsule; Heptagon (7 sided); Trapezoid; Oval; Clover; Octagon (8 sided); Tear; Freeform.
   */
  Shape_2: v3_ActCode_Shape_2,
  /**
   * Definition: A characteristic of an oral solid dosage form of a medicinal product, specifying the longest single dimension of the solid dosage form as a physical quantity in the dimension of length (e.g., 3 mm). The length is should be specified in millimeters and should be rounded to the nearest whole millimeter.
   *
   *
   *                            Example: SPLSIZE for a rectangular shaped tablet is the length and SPLSIZE for a round shaped tablet is the diameter.
   */
  Size_2: v3_ActCode_Size_2,
  /**
   * Definition: A characteristic of an oral solid dosage form of a medicinal product, to describe whether or not the medicinal product has a mark or symbol appearing on it for easy and definite recognition.  Score lines, letters, numbers, and internal and external cut-outs are not considered marks or symbols. See SPLSCORING and SPLIMPRINT for these characteristics.
   *
   *
   *                            Constraints: The Observation.value must be a Boolean (BL) with <u>true</u> indicating the presence and <u>false</u> for the absence of marks or symbols.
   *
   *
   *                            Example:
   */
  Symbol: v3_ActCode_Symbol,
  /**
   * Describes the strata for which the measure is to be evaluated. There are three examples of reasons for stratification based on existing work. These include: (1) evaluate the measure based on different age groupings within the population described in the measure (e.g., evaluate the whole [age 14-25] and each sub-stratum [14-19] and [20-25]); (2) evaluate the eMeasure based on either a specific condition, a specific discharge location, or both; (3) evaluate the eMeasure based on different locations within a facility (e.g., evaluate the overall rate for all intensive care units and also some strata include additional findings [specific birth weights for neonatal intensive care units]).
   */
  Stratification: v3_ActCode_Stratification,
  /**
   * Proposed therapy may be inappropriate or ineffective because the start of administration is too late after the onset of the condition
   */
  StartTooLateAlert_2: v3_ActCode_StartTooLateAlert_2,
  /**
   * Description: One or more records in the query response have been suppressed due to consent or privacy restrictions.
   */
  RecordSuppressed: v3_ActCode_RecordSuppressed,
  /**
   * Description: The patient's teacher when immunized.
   */
  Teacher: v3_ActCode_Teacher,
  /**
   * Description:Proposed therapy may be inappropriate or ineffective based on the proposed start or end time.
   */
  TimingDetectedIssue: v3_ActCode_TimingDetectedIssue,
  /**
   * A sequence of values in the "absolute" time domain.  This is the same time domain that all HL7 timestamps use.  It is time as measured by the Gregorian calendar
   */
  AbsoluteTimeSequence: v3_ActCode_AbsoluteTimeSequence,
  /**
   * A sequence of values in a "relative" time domain.  The time is measured relative to the earliest effective time in the Observation Series containing this sequence.
   */
  RelativeTimeSequence: v3_ActCode_RelativeTimeSequence,
  /**
   * Definition:The therapy is being performed at a time which diverges from the time the therapy was requested
   */
  EventTimingIncorrectAlert: v3_ActCode_EventTimingIncorrectAlert,
  /**
   * The patient is receiving a subsequent fill significantly later than would be expected based on the amount previously supplied and the therapy dosage instructions
   */
  RefillTooLateAlert: v3_ActCode_RefillTooLateAlert,
  /**
   * The patient is receiving a subsequent fill significantly earlier than would be expected based on the amount previously supplied and the therapy dosage instructions
   */
  RefillTooSoonAlert: v3_ActCode_RefillTooSoonAlert,
  /**
   * Proposed therapy may interact with an existing or recent therapeutic product
   */
  TherapeuticProductAlert: v3_ActCode_TherapeuticProductAlert,
  /**
   * Can be a URL or hyperlinks that link to the transmission formats that are specified for a particular reporting program.
   */
  TransmissionFormat: v3_ActCode_TransmissionFormat,
  /**
   * Communication of an agent from one living subject to another living subject through direct contact with blood or blood products where the contact with blood is part of  a therapeutic procedure.
   */
  TransfusionTransmission: v3_ActCode_TransfusionTransmission,
  /**
   * Usage notes.
   */
  NoticeOfUse: v3_ActCode_NoticeOfUse,
  /**
   * Indicates that the ICSR is describing a problem with the actual vaccine product such as physical defects (cloudy, particulate matter) or inability to confer immunity.
   */
  VaccineProductProblem: v3_ActCode_VaccineProductProblem,
  /**
   * Description:The specified element did not pass business-rule validation.
   */
  ValidationIssue: v3_ActCode_ValidationIssue,
  /**
   * Communication of an agent from a living subject acting as a required intermediary in the agent transmission process to a recipient living subject via direct contact.
   */
  VectorBorneTransmission: v3_ActCode_VectorBorneTransmission,
  /**
   * An observation that reports the volume of a sample.
   */
  ActSpecObsVolumeCode: v3_ActCode_ActSpecObsVolumeCode,
  /**
   * Communication of an agent from a contaminated water source to a living subject whether the water is ingested as a food or not. The route of entry of the water may be through any bodily orifice.
   */
  WaterBorneTransmission: v3_ActCode_WaterBorneTransmission,
  WGHT: v3_ActCode_WGHT,
};
const v3_ObservationValue_ActCoverageAssessmentObservationValue: Coding = {
  code: '_ActCoverageAssessmentObservationValue',
  display: 'ActCoverageAssessmentObservationValue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ActFinancialStatusObservationValue: Coding = {
  code: '_ActFinancialStatusObservationValue',
  display: 'ActFinancialStatusObservationValue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_AllergyTestValue: Coding = {
  code: '_AllergyTestValue',
  display: 'AllergyTestValue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_AnnotationValue: Coding = {
  code: '_AnnotationValue',
  display: 'AnnotationValue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_CommonClinicalObservation: Coding = {
  code: '_CommonClinicalObservationValue',
  display: 'common clinical observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_CompositeMeasureScoring: Coding = {
  code: '_CompositeMeasureScoring',
  display: 'CompositeMeasureScoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_CoverageLevelObservationValue: Coding = {
  code: '_CoverageLevelObservationValue',
  display: 'CoverageLevelObservationValue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_CoverageLimitObservationValue: Coding = {
  code: '_CoverageLimitObservationValue',
  display: 'CoverageLimitObservationValue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_CriticalityObservationValue: Coding = {
  code: '_CriticalityObservationValue',
  display: 'CriticalityObservationValue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_EmploymentStatus: Coding = {
  code: '_EmploymentStatus',
  display: '_EmploymentStatus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_GeneticObservationValue: Coding = {
  code: '_GeneticObservationValue',
  display: 'GeneticObservationValue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_IndicationValue: Coding = {
  code: '_IndicationValue',
  display: 'IndicationValue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_IndividualCaseSafetyReportValueDomains: Coding = {
  code: '_IndividualCaseSafetyReportValueDomains',
  display: 'Individual Case Safety Report Value Domains',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ObservationMeasureScoring: Coding = {
  code: '_ObservationMeasureScoring',
  display: 'ObservationMeasureScoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ObservationMeasureType: Coding = {
  code: '_ObservationMeasureType',
  display: 'ObservationMeasureType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_PartialCompletionScale: Coding = {
  code: '_PartialCompletionScale',
  display: 'PartialCompletionScale',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_AlterationIntegrity: Coding = {
  code: '_SECALTINTOBV',
  display: 'alteration integrity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_DataIntegrity: Coding = {
  code: '_SECDATINTOBV',
  display: 'data integrity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_IntegrityConfidence: Coding = {
  code: '_SECINTCONOBV',
  display: 'integrity confidence',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SecurityIntegrity: Coding = {
  code: '_SECINTOBV',
  display: 'security integrity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ProvenanceAssertedBy: Coding = {
  code: '_SECINTPRVABOBV',
  display: 'provenance asserted by',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Provenance: Coding = {
  code: '_SECINTPRVOBV',
  display: 'provenance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ProvenanceReportedBy: Coding = {
  code: '_SECINTPRVRBOBV',
  display: 'provenance reported by',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SecurityObservationValue: Coding = {
  code: '_SecurityObservationValue',
  display: 'SecurityObservationValue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SeverityObservation: Coding = {
  code: '_SeverityObservation',
  display: 'SeverityObservation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SubjectBodyPosition: Coding = {
  code: '_SubjectBodyPosition',
  display: '_SubjectBodyPosition',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_VerificationOutcome: Coding = {
  code: '_VerificationOutcomeValue',
  display: 'verification outcome',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_WorkSchedule: Coding = {
  code: '_WorkSchedule',
  display: '_WorkSchedule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_NoReaction: Coding = {
  code: 'A0',
  display: 'no reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MinimalReaction: Coding = {
  code: 'A1',
  display: 'minimal reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MildReaction: Coding = {
  code: 'A2',
  display: 'mild reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ModerateReaction: Coding = {
  code: 'A3',
  display: 'moderate reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SevereReaction: Coding = {
  code: 'A4',
  display: 'severe reaction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Abstracted: Coding = {
  code: 'ABSTRED',
  display: 'abstracted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_AbuseVictim: Coding = {
  code: 'ABUSE',
  display: 'abuse victim',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ActiveCoverage: Coding = {
  code: 'ACT',
  display: 'active coverage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ActivePendingInvestigation: Coding = {
  code: 'ACTPEND',
  display: 'active - pending investigation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_AdultChild: Coding = {
  code: 'ADC',
  display: 'adult child',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_AdoptionDocument: Coding = {
  code: 'ADOPT',
  display: 'adoption document',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Aggregated: Coding = {
  code: 'AGGRED',
  display: 'aggregated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_AllOrNothingScoring: Coding = {
  code: 'ALLORNONESCR',
  display: 'All-or-nothing Scoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Alone: Coding = {
  code: 'ALONE',
  display: 'alone',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Annuity: Coding = {
  code: 'ANNUITY',
  display: 'annuity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Anonymized: Coding = {
  code: 'ANONYED',
  display: 'anonymized',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_AppropriateUseProcessMeasure: Coding = {
  code: 'APPROPRIATE',
  display: 'appropriate use process measure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Asset: Coding = {
  code: 'ASSET',
  display: 'asset',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_BirthCertificate: Coding = {
  code: 'BTHCERT',
  display: 'birth certificate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_CreditableCoverageDocument: Coding = {
  code: 'CCOC',
  display: 'creditable coverage document',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Child: Coding = {
  code: 'CHD',
  display: 'child',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ChildSupport: Coding = {
  code: 'CHILD',
  display: 'child support',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ClinicianAsserted: Coding = {
  code: 'CLINAST',
  display: 'clinician asserted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ClinicianReported: Coding = {
  code: 'CLINRPT',
  display: 'clinician reported',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ClothingExpense: Coding = {
  code: 'CLOTH',
  display: 'clothing expense',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_CohortMeasureScoring: Coding = {
  code: 'COHORT',
  display: 'cohort measure scoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_CompositeMeasureType: Coding = {
  code: 'COMPOSITE',
  display: 'composite measure type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ContinuousVariableMeasureScoring: Coding = {
  code: 'CONTVAR',
  display: 'continuous variable measure scoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HighCriticality: Coding = {
  code: 'CRITH',
  display: 'high criticality',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LowCriticality: Coding = {
  code: 'CRITL',
  display: 'low criticality',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_UnableToAssessCriticality: Coding = {
  code: 'CRITU',
  display: 'unable to assess criticality',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_CryptographicHashFunction: Coding = {
  code: 'CRYTOHASH',
  display: 'cryptographic hash function',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Dependent: Coding = {
  code: 'DEP',
  display: 'dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_DependentChildren: Coding = {
  code: 'DEPCHD',
  display: 'dependent children',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_DependentSpouse: Coding = {
  code: 'DEPSPS',
  display: 'dependent spouse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_DependentYoungChildren: Coding = {
  code: 'DEPYGCHD',
  display: 'dependent young children',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_DeviceAsserted: Coding = {
  code: 'DEVAST',
  display: 'device asserted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_DeviceReported: Coding = {
  code: 'DEVRPT',
  display: 'device reported',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_DigitalSignature: Coding = {
  code: 'DIGSIG',
  display: 'digital signature',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_DisabilityPay: Coding = {
  code: 'DISABL',
  display: 'disability pay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Disabled: Coding = {
  code: 'DISABLE',
  display: 'disabled',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_DomesticPartner: Coding = {
  code: 'DP',
  display: 'domestic partner',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_DriverLicense: Coding = {
  code: 'DRLIC',
  display: 'driver license',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_DrugUse: Coding = {
  code: 'DRUG',
  display: 'drug use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_DaytimeShift: Coding = {
  code: 'DS',
  display: 'daytime shift',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Employee: Coding = {
  code: 'ECH',
  display: 'employee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_EfficiencyMeasureType: Coding = {
  code: 'EFFICIENCY',
  display: 'efficiency measure type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Eligible: Coding = {
  code: 'ELG',
  display: 'eligible',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_EligibilityIndicator: Coding = {
  code: 'ELSTAT',
  display: 'eligibility indicator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_EarlyMorningShift: Coding = {
  code: 'EMS',
  display: 'early morning shift',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_EveningShift: Coding = {
  code: 'ES',
  display: 'evening shift',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ExperienceMeasureType: Coding = {
  code: 'EXPERIENCE',
  display: 'experience measure type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LiveWithFamily: Coding = {
  code: 'FAM',
  display: 'live with family',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_FamilyCoverage: Coding = {
  code: 'FLY',
  display: 'family coverage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_FoodExpense: Coding = {
  code: 'FOOD',
  display: 'food expense',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_FosterChildDocument: Coding = {
  code: 'FOSTER',
  display: 'foster child document',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_GreatExtent: Coding = {
  code: 'G',
  display: 'Great extent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_High: Coding = {
  code: 'H',
  display: 'High',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HealthcareProfessionalAsserted: Coding = {
  code: 'HCPAST',
  display: 'healthcare professional asserted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HealthcareProfessionalReported: Coding = {
  code: 'HCPRPT',
  display: 'healthcare professional reported',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HealthExpense: Coding = {
  code: 'HEALTH',
  display: 'health expense',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HealthStatus: Coding = {
  code: 'HLSTAT',
  display: 'health status',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Homeless: Coding = {
  code: 'HMLESS',
  display: 'homeless',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HOMO: Coding = {
  code: 'Homozygote',
  display: 'HOMO',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HouseholdExpense: Coding = {
  code: 'HOUSE',
  display: 'household expense',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HighlyReliable: Coding = {
  code: 'HRELIABLE',
  display: 'highly reliable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_IllegalImmigrant: Coding = {
  code: 'ILGIM',
  display: 'illegal immigrant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Inactive: Coding = {
  code: 'INACT',
  display: 'inactive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Incarcerated: Coding = {
  code: 'INCAR',
  display: 'incarcerated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Income: Coding = {
  code: 'INCOME',
  display: 'income',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Individual: Coding = {
  code: 'IND',
  display: 'individual',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_InactivePendingInvestigation: Coding = {
  code: 'INPNDINV',
  display: 'inactive - pending investigation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_InactivePendingEligibilityUpdate: Coding = {
  code: 'INPNDUPD',
  display: 'inactive - pending eligibility update',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_IntermediateClinicalOutcomeMeasure: Coding = {
  code: 'INTERM-OM',
  display: 'intermediate clinical outcome measure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_InvestmentIncome: Coding = {
  code: 'INVEST',
  display: 'investment income',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_IVDrugUse: Coding = {
  code: 'IVDRG',
  display: 'IV drug use',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Low: Coding = {
  code: 'L',
  display: 'Low',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LargeExtent: Coding = {
  code: 'LE',
  display: 'Large extent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LegalExpense: Coding = {
  code: 'LEGAL',
  display: 'legal expense',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LinearScoring: Coding = {
  code: 'LINEARSCR',
  display: 'Linear Scoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LivingDependency: Coding = {
  code: 'LIVDEP',
  display: 'living dependency',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LivingExpense: Coding = {
  code: 'LIVEXP',
  display: 'living expense',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LivingSituation: Coding = {
  code: 'LIVSIT',
  display: 'living situation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LeftLateralDecubitus: Coding = {
  code: 'LLD',
  display: 'left lateral decubitus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_AuthenticationLevelOfAssuranceValue: Coding = {
  code: 'LOAAN',
  display: 'authentication level of assurance value',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LowAuthenticationLevelOfAssurance: Coding = {
  code: 'LOAAN1',
  display: 'low authentication level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_BasicAuthenticationLevelOfAssurance: Coding = {
  code: 'LOAAN2',
  display: 'basic authentication level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MediumAuthenticationLevelOfAssurance: Coding = {
  code: 'LOAAN3',
  display: 'medium authentication level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HighAuthenticationLevelOfAssurance: Coding = {
  code: 'LOAAN4',
  display: 'high authentication level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_AuthenticationProcessLevelOfAssuranceValue: Coding = {
  code: 'LOAAP',
  display: 'authentication process level of assurance value',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LowAuthenticationProcessLevelOfAssurance: Coding = {
  code: 'LOAAP1',
  display: 'low authentication process level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_BasicAuthenticationProcessLevelOfAssurance: Coding = {
  code: 'LOAAP2',
  display: 'basic authentication process level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MediumAuthenticationProcessLevelOfAssurance: Coding = {
  code: 'LOAAP3',
  display: 'medium authentication process level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HighAuthenticationProcessLevelOfAssurance: Coding = {
  code: 'LOAAP4',
  display: 'high authentication process level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_AssertionLevelOfAssuranceValue: Coding = {
  code: 'LOAAS',
  display: 'assertion level of assurance value',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LowAssertionLevelOfAssurance: Coding = {
  code: 'LOAAS1',
  display: 'low assertion level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_BasicAssertionLevelOfAssurance: Coding = {
  code: 'LOAAS2',
  display: 'basic assertion level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MediumAssertionLevelOfAssurance: Coding = {
  code: 'LOAAS3',
  display: 'medium assertion level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HighAssertionLevelOfAssurance: Coding = {
  code: 'LOAAS4',
  display: 'high assertion level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_TokenAndCredentialManagementLevelOfAssuranceValue: Coding = {
  code: 'LOACM',
  display: 'token and credential management level of assurance value)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LowTokenAndCredentialManagementLevelOfAssurance: Coding = {
  code: 'LOACM1',
  display: 'low token and credential management level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_BasicTokenAndCredentialManagementLevelOfAssurance: Coding = {
  code: 'LOACM2',
  display: 'basic token and credential management level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MediumTokenAndCredentialManagementLevelOfAssurance: Coding = {
  code: 'LOACM3',
  display: 'medium token and credential management level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HighTokenAndCredentialManagementLevelOfAssurance: Coding = {
  code: 'LOACM4',
  display: 'high token and credential management level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_IdentityProofingLevelOfAssurance: Coding = {
  code: 'LOAID',
  display: 'identity proofing level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LowIdentityProofingLevelOfAssurance: Coding = {
  code: 'LOAID1',
  display: 'low identity proofing level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_BasicIdentityProofingLevelOfAssurance: Coding = {
  code: 'LOAID2',
  display: 'basic identity proofing level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MediumIdentityProofingLevelOfAssurance: Coding = {
  code: 'LOAID3',
  display: 'medium identity proofing level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HighIdentityProofingLevelOfAssurance: Coding = {
  code: 'LOAID4',
  display: 'high identity proofing level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_NonRepudiationLevelOfAssuranceValue: Coding = {
  code: 'LOANR',
  display: 'non-repudiation level of assurance value',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LowNonRepudiationLevelOfAssurance: Coding = {
  code: 'LOANR1',
  display: 'low non-repudiation level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_BasicNonRepudiationLevelOfAssurance: Coding = {
  code: 'LOANR2',
  display: 'basic non-repudiation level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MediumNonRepudiationLevelOfAssurance: Coding = {
  code: 'LOANR3',
  display: 'medium non-repudiation level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HighNonRepudiationLevelOfAssurance: Coding = {
  code: 'LOANR4',
  display: 'high non-repudiation level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_RemoteAccessLevelOfAssuranceValue: Coding = {
  code: 'LOARA',
  display: 'remote access level of assurance value',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LowRemoteAccessLevelOfAssurance: Coding = {
  code: 'LOARA1',
  display: 'low remote access level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_BasicRemoteAccessLevelOfAssurance: Coding = {
  code: 'LOARA2',
  display: 'basic remote access level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MediumRemoteAccessLevelOfAssurance: Coding = {
  code: 'LOARA3',
  display: 'medium remote access level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HighRemoteAccessLevelOfAssurance: Coding = {
  code: 'LOARA4',
  display: 'high remote access level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_TokenLevelOfAssuranceValue: Coding = {
  code: 'LOATK',
  display: 'token level of assurance value',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_LowTokenLevelOfAssurance: Coding = {
  code: 'LOATK1',
  display: 'low token level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_BasicTokenLevelOfAssurance: Coding = {
  code: 'LOATK2',
  display: 'basic token level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MediumTokenLevelOfAssurance: Coding = {
  code: 'LOATK3',
  display: 'medium token level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_HighTokenLevelOfAssurance: Coding = {
  code: 'LOATK4',
  display: 'high token level of assurance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Moderate: Coding = {
  code: 'M',
  display: 'Moderate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Mapped: Coding = {
  code: 'MAPPED',
  display: 'mapped',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Masked: Coding = {
  code: 'MASKED',
  display: 'masked',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MediumExtent: Coding = {
  code: 'ME',
  display: 'Medium extent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ProgramOrPolicyMember: Coding = {
  code: 'MEMBER',
  display: 'program or policy member',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MinimalExtent: Coding = {
  code: 'MI',
  display: 'Minimal extent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MilitaryIdentification: Coding = {
  code: 'MIL',
  display: 'military identification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Mortgage: Coding = {
  code: 'MORTG',
  display: 'mortgage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_MarriageCertificate: Coding = {
  code: 'MRGCERT',
  display: 'marriage certificate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_None: Coding = {
  code: 'N',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_NotEligible: Coding = {
  code: 'NELG',
  display: 'not eligible',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_NightShift: Coding = {
  code: 'NS',
  display: 'night shift',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_OpportunityScoring: Coding = {
  code: 'OPPORSCR',
  display: 'Opportunity Scoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_OutcomeMeasureType: Coding = {
  code: 'OUTCOME',
  display: 'outcome measure type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_PatientAcquaintanceAsserted: Coding = {
  code: 'PACQAST',
  display: 'patient acquaintance asserted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_PatientAcquaintanceReported: Coding = {
  code: 'PACQRPT',
  display: 'patient acquaintance reported',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Passport: Coding = {
  code: 'PASSPORT',
  display: 'passport',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_PatientAsserted: Coding = {
  code: 'PATAST',
  display: 'patient asserted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_PatientReported: Coding = {
  code: 'PATRPT',
  display: 'patient reported',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_PaidEmployment: Coding = {
  code: 'PAY',
  display: 'paid employment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_PayerAsserted: Coding = {
  code: 'PAYAST',
  display: 'payer asserted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_PayerReported: Coding = {
  code: 'PAYRPT',
  display: 'payer reported',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Pregnant: Coding = {
  code: 'PGNT',
  display: 'pregnant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Prone: Coding = {
  code: 'PRN',
  display: 'prone',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ProfessionalAsserted: Coding = {
  code: 'PROAST',
  display: 'professional asserted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Probation: Coding = {
  code: 'PROB',
  display: 'probation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ProcessMeasureType: Coding = {
  code: 'PROCESS',
  display: 'process measure type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_RealProperty: Coding = {
  code: 'PROP',
  display: 'real property',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_IntermediateClinicalOutcomeMeasure_2: Coding = {
  code: 'PRO-PM',
  display: 'intermediate clinical outcome measure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ProportionMeasureScoring: Coding = {
  code: 'PROPOR',
  display: 'proportion measure scoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ProfessionalReported: Coding = {
  code: 'PRORPT',
  display: 'professional reported',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Pseudonymized: Coding = {
  code: 'PSEUDED',
  display: 'pseudonymized',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_RatioMeasureScoring: Coding = {
  code: 'RATIO',
  display: 'ratio measure scoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Redacted: Coding = {
  code: 'REDACTED',
  display: 'redacted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Refugee: Coding = {
  code: 'REFUG',
  display: 'refugee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Relative: Coding = {
  code: 'RELAT',
  display: 'relative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_RelativeDependent: Coding = {
  code: 'RELDEP',
  display: 'relative dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Reliable: Coding = {
  code: 'RELIABLE',
  display: 'reliable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Rent: Coding = {
  code: 'RENT',
  display: 'rent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ResourceUseMeasureType: Coding = {
  code: 'RESOURCE',
  display: 'resource use measure type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_RetirementInvestmentAccount: Coding = {
  code: 'RETACCT',
  display: 'retirement investment account',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_RetirementPay: Coding = {
  code: 'RETIRE',
  display: 'retirement pay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_RightLateralDecubitus: Coding = {
  code: 'RLD',
  display: 'right lateral decubitus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_RotatingShiftWithNights: Coding = {
  code: 'RSWN',
  display: 'rotating shift with nights',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_RotatingShiftWithoutNights: Coding = {
  code: 'RSWON',
  display: 'rotating shift without nights',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_ReverseTrendelenburg: Coding = {
  code: 'RTRD',
  display: 'reverse trendelenburg',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SomeExtent: Coding = {
  code: 'S',
  display: 'Some extent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SubstituteDecisionMakerAsserted: Coding = {
  code: 'SDMAST',
  display: 'substitute decision maker asserted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SubstituteDecisionMakerReported: Coding = {
  code: 'SDMRPT',
  display: 'substitute decision maker reported',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SecurityTrustObservation: Coding = {
  code: 'SECTRSTOBV',
  display: 'security trust observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SemiFowlerQuoteS: Coding = {
  code: 'SFWL',
  display: "Semi-Fowler's",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Sitting: Coding = {
  code: 'SIT',
  display: 'sitting',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SocioEconomicStatus: Coding = {
  code: 'SOECSTAT',
  display: 'socio economic status',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SpousalOrPartnerSupport: Coding = {
  code: 'SPOUSAL',
  display: 'spousal or partner support',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SpouseOnly: Coding = {
  code: 'SPS',
  display: 'spouse only',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SpouseDependent: Coding = {
  code: 'SPSDEP',
  display: 'spouse dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SplitShift: Coding = {
  code: 'SS',
  display: 'split shift',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SameSexPartner: Coding = {
  code: 'SSP',
  display: 'same sex partner',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Standing: Coding = {
  code: 'STN',
  display: 'standing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_StructureMeasureType: Coding = {
  code: 'STRUCTURE',
  display: 'structure measure type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_StudentEnrollment: Coding = {
  code: 'STUDENRL',
  display: 'student enrollment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Subsetted: Coding = {
  code: 'SUBSETTED',
  display: 'subsetted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SundryExpense: Coding = {
  code: 'SUNDRY',
  display: 'sundry expense',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Supine: Coding = {
  code: 'SUP',
  display: 'supine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_IncomeSupplement: Coding = {
  code: 'SUPPLE',
  display: 'income supplement',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_SyntacticTransform: Coding = {
  code: 'SYNTAC',
  display: 'syntactic transform',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_TaxObligation: Coding = {
  code: 'TAX',
  display: 'tax obligation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_TransportationExpense: Coding = {
  code: 'TRANS',
  display: 'transportation expense',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Trendelenburg: Coding = {
  code: 'TRD',
  display: 'trendelenburg',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Translated: Coding = {
  code: 'TRSLT',
  display: 'translated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_TrustAccreditationObservation: Coding = {
  code: 'TRSTACCRDOBV',
  display: 'trust accreditation observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_TrustAgreementObservation: Coding = {
  code: 'TRSTAGREOBV',
  display: 'trust agreement observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_TrustCertificateObservation: Coding = {
  code: 'TRSTCERTOBV',
  display: 'trust certificate observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_TrustAssuranceObservation: Coding = {
  code: 'TRSTLOAOBV',
  display: 'trust assurance observation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_NoneSupplied6: Coding = {
  code: 'TRSTMECOBV',
  display: 'none supplied 6',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Trust: Coding = {
  code: 'TRUST',
  display: 'trust',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_UncertainReliability: Coding = {
  code: 'UNCERTREL',
  display: 'uncertain reliability',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Unemployed_2: Coding = {
  code: 'UNEMPL',
  display: 'unemployed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_UnrelatedPerson: Coding = {
  code: 'UNREL',
  display: 'unrelated person',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Unreliable: Coding = {
  code: 'UNRELIABLE',
  display: 'unreliable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_UnrelatedPersonDependent: Coding = {
  code: 'URELDEP',
  display: 'unrelated person dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_UtilityExpense: Coding = {
  code: 'UTIL',
  display: 'utility expense',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_Versioned: Coding = {
  code: 'VERSIONED',
  display: 'versioned',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_VeryLongShift: Coding = {
  code: 'VLS',
  display: 'very long shift',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_VariableShift: Coding = {
  code: 'VS',
  display: 'variable shift',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
const v3_ObservationValue_WeightedScoring: Coding = {
  code: 'WEIGHTSCR',
  display: 'Weighted Scoring',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ObservationValue',
};
/**
 *  This domain is the root domain to which all HL7-recognized value sets for the Observation.value attribute will be linked when Observation.value has a coded data type.  OpenIssue:
 * Description copied from Concept Domain of same name.  Must be corrected..
 */
export const V3ObservationValue = {
  /**
   * Codes specify the category of observation, evidence, or document used to assess for services, e.g., discharge planning, or to establish eligibility for coverage under a policy or program. The type of evidence is coded as observation values.
   */
  ActCoverageAssessmentObservationValue: v3_ObservationValue_ActCoverageAssessmentObservationValue,
  /**
   * Code specifying financial indicators used to assess or establish eligibility for coverage under a policy or program; e.g., pay stub; tax or income document; asset document; living expenses.
   */
  ActFinancialStatusObservationValue: v3_ObservationValue_ActFinancialStatusObservationValue,
  /**
   * Indicates the result of a particular allergy test.  E.g. Negative, Mild, Moderate, Severe
   */
  AllergyTestValue: v3_ObservationValue_AllergyTestValue,
  /**
   * AnnotationValue
   */
  AnnotationValue: v3_ObservationValue_AnnotationValue,
  /**
   * Description:Used in a patient care message to value simple clinical (non-lab) observations.
   */
  CommonClinicalObservation: v3_ObservationValue_CommonClinicalObservation,
  /**
   * Observation values that communicate the method used in a quality measure to combine the component measure results included in an composite measure.
   */
  CompositeMeasureScoring: v3_ObservationValue_CompositeMeasureScoring,
  /**
   * Description:Coded observation values for types of covered parties under a policy or program based on their personal relationships or employment status.
   */
  CoverageLevelObservationValue: v3_ObservationValue_CoverageLevelObservationValue,
  /**
   * Description:Coded observation values for coverage limitations, for e.g., types of claims or types of parties covered under a policy or program.
   */
  CoverageLimitObservationValue: v3_ObservationValue_CoverageLimitObservationValue,
  /**
   * A clinical judgment as to the worst case result of a future exposure (including substance administration). When the worst case result is assessed to have a life-threatening or organ system threatening potential, it is considered to be of high criticality.
   */
  CriticalityObservationValue: v3_ObservationValue_CriticalityObservationValue,
  /**
   * Concepts representing whether a person does or does not currently have a job or is not currently in the labor pool seeking employment.
   */
  EmploymentStatus: v3_ObservationValue_EmploymentStatus,
  /**
   * Description: The domain contains genetic analysis specific observation values, e.g. Homozygote, Heterozygote, etc.
   */
  GeneticObservationValue: v3_ObservationValue_GeneticObservationValue,
  /**
   * Indicates the specific observation result which is the reason for the action (prescription, lab test, etc.). E.g. Headache, Ear infection, planned diagnostic image (requiring contrast agent), etc.
   */
  IndicationValue: v3_ObservationValue_IndicationValue,
  /**
   * This domain is established as a parent to a variety of value domains being defined to support the communication of Individual Case Safety Reports to regulatory bodies. Arguably, this aggregation is not taxonomically pure, but the grouping will facilitate the management of these domains.
   */
  IndividualCaseSafetyReportValueDomains: v3_ObservationValue_IndividualCaseSafetyReportValueDomains,
  /**
   * Observation values used to indicate the type of scoring (e.g. proportion, ratio) used by a health quality measure.
   */
  ObservationMeasureScoring: v3_ObservationValue_ObservationMeasureScoring,
  /**
   * Observation values used to indicate what kind of health quality measure is used.
   */
  ObservationMeasureType: v3_ObservationValue_ObservationMeasureType,
  /**
   * PartialCompletionScale
   */
  PartialCompletionScale: v3_ObservationValue_PartialCompletionScale,
  /**
   * Abstract security metadata observation values used to indicate mechanism used for authorized alteration of an IT resource (data, information object, service, or system capability)
   */
  AlterationIntegrity: v3_ObservationValue_AlterationIntegrity,
  /**
   * Abstract security observation values used to indicate data integrity metadata.
   *
   *
   *                            Examples: Codes conveying the mechanism used to preserve the accuracy and consistency of an IT resource such as a digital signature and a cryptographic hash function.
   */
  DataIntegrity: v3_ObservationValue_DataIntegrity,
  /**
   * Abstract security observation value used to indicate integrity confidence metadata.
   *
   *
   *                            Examples: Codes conveying the level of reliability and trustworthiness of an IT resource.
   */
  IntegrityConfidence: v3_ObservationValue_IntegrityConfidence,
  /**
   * Abstract security observation values used to indicate security integrity metadata.
   *
   *
   *                            Examples: Codes conveying integrity status, integrity confidence, and provenance.
   */
  SecurityIntegrity: v3_ObservationValue_SecurityIntegrity,
  /**
   * Abstract security provenance metadata observation value used to indicate the entity that asserted an IT resource (data, information object, service, or system capability).
   *
   *
   *                            Examples: Codes conveying the provenance metadata about the entity asserting the resource.
   */
  ProvenanceAssertedBy: v3_ObservationValue_ProvenanceAssertedBy,
  /**
   * Abstract security metadata observation value used to indicate the provenance of an IT resource (data, information object, service, or system capability).
   *
   *
   *                            Examples: Codes conveying the provenance metadata about the entity reporting an IT resource.
   */
  Provenance: v3_ObservationValue_Provenance,
  /**
   * Abstract security provenance metadata observation value used to indicate the entity that reported the resource (data, information object, service, or system capability).
   *
   *
   *                            Examples: Codes conveying the provenance metadata about the entity reporting an IT resource.
   */
  ProvenanceReportedBy: v3_ObservationValue_ProvenanceReportedBy,
  /**
   * Observation values used to indicate security observation metadata.
   */
  SecurityObservationValue: v3_ObservationValue_SecurityObservationValue,
  /**
   * Potential values for observations of severity.
   */
  SeverityObservation: v3_ObservationValue_SeverityObservation,
  /**
   * Contains codes for defining the observed, physical position of a subject, such as during an observation, assessment, collection of a specimen, etc.  ECG waveforms and vital signs, such as blood pressure, are two examples where a general, observed position typically needs to be noted.
   */
  SubjectBodyPosition: v3_ObservationValue_SubjectBodyPosition,
  /**
   * Values for observations of verification act results
   *
   *
   *                            Examples: Verified, not verified, verified with warning.
   */
  VerificationOutcome: v3_ObservationValue_VerificationOutcome,
  /**
   * Concepts that describe an individual's typical arrangement of working hours for an occupation.
   */
  WorkSchedule: v3_ObservationValue_WorkSchedule,
  /**
   * Description:Patient exhibits no reaction to the challenge agent.
   */
  NoReaction: v3_ObservationValue_NoReaction,
  /**
   * Description:Patient exhibits a minimal reaction to the challenge agent.
   */
  MinimalReaction: v3_ObservationValue_MinimalReaction,
  /**
   * Description:Patient exhibits a mild reaction to the challenge agent.
   */
  MildReaction: v3_ObservationValue_MildReaction,
  /**
   * Description:Patient exhibits moderate reaction to the challenge agent.
   */
  ModerateReaction: v3_ObservationValue_ModerateReaction,
  /**
   * Description:Patient exhibits a severe reaction to the challenge agent.
   */
  SevereReaction: v3_ObservationValue_SevereReaction,
  /**
   * Security metadata observation values used to indicate the use of a more abstract version of the content, e.g., replacing exact value of an age or date field with a range, or remove the left digits of a credit card number or SSN.
   */
  Abstracted: v3_ObservationValue_Abstracted,
  /**
   * Indication of abuse victim.
   */
  AbuseVictim: v3_ObservationValue_AbuseVictim,
  /**
   * Definition: Coverage is in effect for healthcare service(s) and/or product(s).
   */
  ActiveCoverage: v3_ObservationValue_ActiveCoverage,
  /**
   * Definition: Coverage is in effect for healthcare service(s) and/or product(s) - Pending Investigation
   */
  ActivePendingInvestigation: v3_ObservationValue_ActivePendingInvestigation,
  /**
   * Description:Child over an age as specified by coverage policy or program, e.g., student, differently abled, and income dependent.
   */
  AdultChild: v3_ObservationValue_AdultChild,
  /**
   * Indicator of adoption.
   */
  AdoptionDocument: v3_ObservationValue_AdoptionDocument,
  /**
   * Security metadata observation values used to indicate the use of an algorithmic combination of actual values with the result of an aggregate function, e.g., average, sum, or count in order to limit disclosure of an IT resource (data, information object, service, or system capability) to the minimum necessary.
   */
  Aggregated: v3_ObservationValue_Aggregated,
  /**
   * Code specifying that the measure uses all-or-nothing scoring. All-or-nothing scoring places an individual in the numerator of the composite measure if and only if they are in the numerator of all component measures in which they are in the denominator.
   */
  AllOrNothingScoring: v3_ObservationValue_AllOrNothingScoring,
  /**
   * Living alone.  Maps to PD1-2   Living arrangement   (IS)   00742 [A]
   */
  Alone: v3_ObservationValue_Alone,
  /**
   * Indicator of annuity ownership or status as beneficiary.
   */
  Annuity: v3_ObservationValue_Annuity,
  /**
   * Security metadata observation value conveying the alteration integrity of an IT resource (data, information object, service, or system capability) by used to indicate the mechanism by which software systems can strip portions of the resource that could allow the identification of the source of the information or the information subject.  No key to relink the data is retained.
   */
  Anonymized: v3_ObservationValue_Anonymized,
  /**
   * A measure that assesses the use of one or more processes where the expected health benefit exceeds the expected negative consequences.
   */
  AppropriateUseProcessMeasure: v3_ObservationValue_AppropriateUseProcessMeasure,
  /**
   * Codes specifying asset indicators used to assess or establish eligibility for coverage under a policy or program.
   */
  Asset: v3_ObservationValue_Asset,
  /**
   * Indicator of birth.
   */
  BirthCertificate: v3_ObservationValue_BirthCertificate,
  /**
   * Indicator of creditable coverage.
   */
  CreditableCoverageDocument: v3_ObservationValue_CreditableCoverageDocument,
  /**
   * Description:Dependent biological, adopted, foster child as specified by coverage policy or program.
   */
  Child: v3_ObservationValue_Child,
  /**
   * Indicator of child support payments received or provided.
   */
  ChildSupport: v3_ObservationValue_ChildSupport,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a clinician.
   */
  ClinicianAsserted: v3_ObservationValue_ClinicianAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a clinician.
   */
  ClinicianReported: v3_ObservationValue_ClinicianReported,
  /**
   * Indicator of clothing expenses.
   */
  ClothingExpense: v3_ObservationValue_ClothingExpense,
  /**
   * A measure in which either short-term cross-section or long-term longitudinal analysis is performed over a group of subjects defined by a set of common properties or defining characteristics (e.g., Male smokers between the ages of 40 and 50 years, exposure to treatment, exposure duration).
   */
  CohortMeasureScoring: v3_ObservationValue_CohortMeasureScoring,
  /**
   * A measure that is composed from one or more other measures and indicates an overall summary of those measures.
   */
  CompositeMeasureType: v3_ObservationValue_CompositeMeasureType,
  /**
   * A measure score in which each individual value for the measure can fall anywhere along a continuous scale (e.g., mean time to thrombolytics which aggregates the time in minutes from a case presenting with chest pain to the time of administration of thrombolytics).
   */
  ContinuousVariableMeasureScoring: v3_ObservationValue_ContinuousVariableMeasureScoring,
  /**
   * Worst case result of a future exposure is assessed to be life-threatening or having high potential for organ system failure.
   */
  HighCriticality: v3_ObservationValue_HighCriticality,
  /**
   * Worst case result of a future exposure is not assessed to be life-threatening or having high potential for organ system failure.
   */
  LowCriticality: v3_ObservationValue_LowCriticality,
  /**
   * Unable to assess the worst case result of a future exposure.
   */
  UnableToAssessCriticality: v3_ObservationValue_UnableToAssessCriticality,
  /**
   * Security metadata observation value used to indicate the mechanism by which software systems can establish that data was not modified in transit.
   *
   *
   *                            Rationale: This definition is intended to align with the ISO 22600-2 3.3.19 definition of cryptographic checkvalue: Information which is derived by performing a cryptographic transformation (see cryptography) on the data unit.  The derivation of the checkvalue may be performed in one or more steps and is a result of a mathematical function of the key and a data unit. It is usually used to check the integrity of a data unit.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            SHA-1
   *                            SHA-2 (Secure Hash Algorithm)
   */
  CryptographicHashFunction: v3_ObservationValue_CryptographicHashFunction,
  /**
   * Description:Person requiring functional and/or financial assistance from another person as specified by coverage policy or program.
   */
  Dependent: v3_ObservationValue_Dependent,
  /**
   * Living with one or more dependent children requiring moderate supervision.
   */
  DependentChildren: v3_ObservationValue_DependentChildren,
  /**
   * Living with disabled spouse requiring functional and health care assistance
   */
  DependentSpouse: v3_ObservationValue_DependentSpouse,
  /**
   * Living with one or more dependent children requiring intensive supervision
   */
  DependentYoungChildren: v3_ObservationValue_DependentYoungChildren,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a device.
   */
  DeviceAsserted: v3_ObservationValue_DeviceAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a device.
   */
  DeviceReported: v3_ObservationValue_DeviceReported,
  /**
   * Security metadata observation value used to indicate the mechanism by which software systems use digital signature to establish that data has not been modified.
   *
   *
   *                            Rationale: This definition is intended to align with the ISO 22600-2 3.3.26 definition of digital signature:  Data appended to, or a cryptographic transformation (see cryptography) of, a data unit that allows a recipient of the data unit to prove the source and integrity of the data unit and protect against forgery e.g., by the recipient.
   */
  DigitalSignature: v3_ObservationValue_DigitalSignature,
  /**
   * Indicator of disability income replacement payment.
   */
  DisabilityPay: v3_ObservationValue_DisabilityPay,
  /**
   * Indication of disability.
   */
  Disabled: v3_ObservationValue_Disabled,
  /**
   * Description:Persons registered as a family unit in a domestic partner registry as specified by law and by coverage policy or program.
   */
  DomesticPartner: v3_ObservationValue_DomesticPartner,
  /**
   * Indicator of driving status.
   */
  DriverLicense: v3_ObservationValue_DriverLicense,
  /**
   * Indication of drug use.
   */
  DrugUse: v3_ObservationValue_DrugUse,
  /**
   * A person who is scheduled for work during daytime hours (for example between 6am and 6pm) on a regular basis.
   */
  DaytimeShift: v3_ObservationValue_DaytimeShift,
  /**
   * Description:An individual employed by an employer who receive remuneration in wages, salary, commission, tips, piece-rates, or pay-in-kind through the employeraTMs payment system (i.e., not a contractor) as specified by coverage policy or program.
   */
  Employee: v3_ObservationValue_Employee,
  /**
   * A measure related to the efficiency of medical treatment.
   */
  EfficiencyMeasureType: v3_ObservationValue_EfficiencyMeasureType,
  /**
   * Definition: Coverage is in effect for healthcare service(s) and/or product(s).
   */
  Eligible: v3_ObservationValue_Eligible,
  /**
   * Code specifying eligibility indicators used to assess or establish eligibility for coverage under a policy or program eligibility status, e.g., certificates of creditable coverage; student enrollment; adoption, marriage or birth certificate.
   */
  EligibilityIndicator: v3_ObservationValue_EligibilityIndicator,
  /**
   * Individuals who, during the last week: a) did any work for at least 1 hour as paid or unpaid employees of a business or government organization; worked in their own businesses, professions, or on their own farms; or b) were not working, but who have a job or business from which the individual was temporarily absent because of vacation, illness, bad weather, childcare problems, maternity or paternity leave, labor-management dispute, job training, or other family or personal reasons, regardless of whether or not they were paid for the time off or were seeking other jobs.
   */
  Employed: v3_ObservationValue_Employed,
  /**
   * Consistent Early morning schedule of 13 hours or less per shift (between 2 am and 2 pm)
   */
  EarlyMorningShift: v3_ObservationValue_EarlyMorningShift,
  /**
   * A person who is scheduled for work during evening hours (for example between 2pm and midnight) on a regular basis.
   */
  EveningShift: v3_ObservationValue_EveningShift,
  /**
   * A measure related to the level of patient engagement or patient experience of care.
   */
  ExperienceMeasureType: v3_ObservationValue_ExperienceMeasureType,
  /**
   * Living with family. Maps to PD1-2   Living arrangement   (IS)   00742 [F]
   */
  LiveWithFamily: v3_ObservationValue_LiveWithFamily,
  /**
   * Description:As specified by coverage policy or program.
   */
  FamilyCoverage: v3_ObservationValue_FamilyCoverage,
  /**
   * Indicator of transportation expenses.
   */
  FoodExpense: v3_ObservationValue_FoodExpense,
  /**
   * Indicator of foster child status.
   */
  FosterChildDocument: v3_ObservationValue_FosterChildDocument,
  /**
   * Value for Act.partialCompletionCode attribute that implies 81-99% completion
   */
  GreatExtent: v3_ObservationValue_GreatExtent,
  /**
   * Indicates the condition may be life-threatening or has the potential to cause permanent injury.
   */
  High: v3_ObservationValue_High,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a healthcare professional.
   */
  HealthcareProfessionalAsserted: v3_ObservationValue_HealthcareProfessionalAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a healthcare professional.
   */
  HealthcareProfessionalReported: v3_ObservationValue_HealthcareProfessionalReported,
  /**
   * Indicator of health expenses; including medication costs, health service costs, financial participations, and health coverage premiums.
   */
  HealthExpense: v3_ObservationValue_HealthExpense,
  /**
   * Code specifying non-clinical indicators related to health status used to assess or establish eligibility for coverage under a policy or program, e.g., pregnancy, disability, drug use, mental health issues.
   */
  HealthStatus: v3_ObservationValue_HealthStatus,
  /**
   * Indication of status as homeless.
   */
  Homeless: v3_ObservationValue_Homeless,
  /**
   * Description: An individual having different alleles at one or more loci regarding a specific character
   */
  HOMO: v3_ObservationValue_HOMO,
  /**
   * Indicator of housing expense, e.g., household appliances, fixtures, furnishings, and maintenance and repairs.
   */
  HouseholdExpense: v3_ObservationValue_HouseholdExpense,
  /**
   * Security metadata observation value used to indicate that the veracity or trustworthiness of an IT resource (data, information object, service, or system capability) for a specified purpose of use is perceived to be or deemed by policy to be very high.
   */
  HighlyReliable: v3_ObservationValue_HighlyReliable,
  /**
   * Indication of status as illegal immigrant.
   */
  IllegalImmigrant: v3_ObservationValue_IllegalImmigrant,
  /**
   * Definition: Coverage is not in effect for healthcare service(s) and/or product(s).
   */
  Inactive: v3_ObservationValue_Inactive,
  /**
   * Indication of status as incarcerated.
   */
  Incarcerated: v3_ObservationValue_Incarcerated,
  /**
   * Code specifying income indicators used to assess or establish eligibility for coverage under a policy or program; e.g., pay or pension check, child support payments received or provided, and taxes paid.
   */
  Income: v3_ObservationValue_Income,
  /**
   * Description:Person as specified by coverage policy or program.
   */
  Individual: v3_ObservationValue_Individual,
  /**
   * Definition: Coverage is not in effect for healthcare service(s) and/or product(s) - Pending Investigation.
   */
  InactivePendingInvestigation: v3_ObservationValue_InactivePendingInvestigation,
  /**
   * Definition: Coverage is not in effect for healthcare service(s) and/or product(s) - Pending Eligibility Update.
   */
  InactivePendingEligibilityUpdate: v3_ObservationValue_InactivePendingEligibilityUpdate,
  /**
   * A measure that evaluates the change over time of a physiologic state observable that is associated with a specific long-term health outcome.
   */
  IntermediateClinicalOutcomeMeasure: v3_ObservationValue_IntermediateClinicalOutcomeMeasure,
  /**
   * Indicator of investment income, e.g., dividend check, annuity payment; real estate rent, investment divestiture proceeds; trust or endowment check.
   */
  InvestmentIncome: v3_ObservationValue_InvestmentIncome,
  /**
   * Indication of IV drug use .
   */
  IVDrugUse: v3_ObservationValue_IVDrugUse,
  /**
   * Indicates the condition may result in some adverse consequences but is unlikely to substantially affect the situation of the subject.
   */
  Low: v3_ObservationValue_Low,
  /**
   * Value for Act.partialCompletionCode attribute that implies 61-80% completion
   */
  LargeExtent: v3_ObservationValue_LargeExtent,
  /**
   * Indicator of legal expenses.
   */
  LegalExpense: v3_ObservationValue_LegalExpense,
  /**
   * Code specifying that the measure uses linear scoring. Linear scoring computes the fraction of component measures in which the individual appears in the numerator, giving equal weight to each component measure.
   */
  LinearScoring: v3_ObservationValue_LinearScoring,
  /**
   * Code specifying observations related to living dependency, such as dependent upon spouse for activities of daily living.
   */
  LivingDependency: v3_ObservationValue_LivingDependency,
  /**
   * Codes specifying living expense indicators used to assess or establish eligibility for coverage under a policy or program.
   */
  LivingExpense: v3_ObservationValue_LivingExpense,
  /**
   * Code specifying observations related to living situation for a person in a private residence.
   */
  LivingSituation: v3_ObservationValue_LivingSituation,
  /**
   * Lying on the left side.
   */
  LeftLateralDecubitus: v3_ObservationValue_LeftLateralDecubitus,
  /**
   * The value assigned as the indicator of the digital quality or reliability of the verification and validation process used to verify the claimed identity of an entity by securely associating an identifier and its authenticator. [Based on ISO 7498-2]
   *
   *                         For example, the degree of confidence in the vetting process used to establish the identity of the individual to whom the credential was issued, and 2) the degree of confidence that the individual who uses the credential is the individual to whom the credential was issued. [OMB M-04-04 E-Authentication Guidance for Federal Agencies]
   */
  AuthenticationLevelOfAssuranceValue: v3_ObservationValue_AuthenticationLevelOfAssuranceValue,
  /**
   * Indicator of low digital quality or reliability of the digital reliability of the verification and validation process used to verify the claimed identity of an entity by securely associating an identifier and its authenticator. [Based on ISO 7498-2]
   *
   *                         The degree of confidence in the vetting process used to establish the identity of the individual to whom the credential was issued, and 2) the degree of confidence that the individual who uses the credential is the individual to whom the credential was issued. [OMB M-04-04 E-Authentication Guidance for Federal Agencies]
   *
   *                         Low authentication level of assurance indicates that the relying party may have little or no confidence in the asserted identity's validity. Level 1 requires little or no confidence in the asserted identity. No identity proofing is required at this level, but the authentication mechanism should provide some assurance that the same claimant is accessing the protected transaction or data. A wide range of available authentication technologies can be employed and any of the token methods of Levels 2, 3, or 4, including Personal Identification Numbers (PINs), may be used. To be authenticated, the claimant must prove control of the token through a secure authentication protocol. At Level 1, long-term shared authentication secrets may be revealed to verifiers.  Assertions issued about claimants as a result of a successful authentication are either cryptographically authenticated by relying parties (using approved methods) or are obtained directly from a trusted party via a secure authentication protocol.   [Summary of the technical requirements specified in NIST SP 800-63 for the four levels of assurance defined by the December 2003, the Office of Management and Budget (OMB) issued Memorandum M-04-04, E-Authentication Guidance for Federal Agencies.]
   */
  LowAuthenticationLevelOfAssurance: v3_ObservationValue_LowAuthenticationLevelOfAssurance,
  /**
   * Indicator of basic digital quality or reliability of the digital reliability of the verification and validation process used to verify the claimed identity of an entity by securely associating an identifier and its authenticator. [Based on ISO 7498-2]
   *
   *                         The degree of confidence in the vetting process used to establish the identity of the individual to whom the credential was issued, and 2) the degree of confidence that the individual who uses the credential is the individual to whom the credential was issued. [OMB M-04-04 E-Authentication Guidance for Federal Agencies]
   *
   *                         Basic authentication level of assurance indicates that the relying party may have some confidence in the asserted identity's validity. Level 2 requires confidence that the asserted identity is accurate. Level 2 provides for single-factor remote network authentication, including identity-proofing requirements for presentation of identifying materials or information. A wide range of available authentication technologies can be employed, including any of the token methods of Levels 3 or 4, as well as passwords. Successful authentication requires that the claimant prove through a secure authentication protocol that the claimant controls the token.  Eavesdropper, replay, and online guessing attacks are prevented.
   * Long-term shared authentication secrets, if used, are never revealed to any party except the claimant and verifiers operated by the CSP; however, session (temporary) shared secrets may be provided to independent verifiers by the CSP. Approved cryptographic techniques are required. Assertions issued about claimants as a result of a successful authentication are either cryptographically authenticated by relying parties (using approved methods) or are obtained directly from a trusted party via a secure authentication protocol.   [Summary of the technical requirements specified in NIST SP 800-63 for the four levels of assurance defined by the December 2003, the Office of Management and Budget (OMB) issued Memorandum M-04-04, E-Authentication Guidance for Federal Agencies.]
   */
  BasicAuthenticationLevelOfAssurance: v3_ObservationValue_BasicAuthenticationLevelOfAssurance,
  /**
   * Indicator of medium digital quality or reliability of the digital reliability of verification and validation of the process used to verify the claimed identity of an entity by securely associating an identifier and its authenticator. [Based on ISO 7498-2]
   *
   *                         The degree of confidence in the vetting process used to establish the identity of the individual to whom the credential was issued, and 2) the degree of confidence that the individual who uses the credential is the individual to whom the credential was issued. [OMB M-04-04 E-Authentication Guidance for Federal Agencies]
   *
   *                         Medium authentication level of assurance indicates that the relying party may have high confidence in the asserted identity's validity.  Level 3 is appropriate for transactions that need high confidence in the accuracy of the asserted identity. Level 3 provides multifactor remote network authentication. At this level, identity-proofing procedures require verification of identifying materials and information. Authentication is based on proof of possession of a key or password through a cryptographic protocol. Cryptographic strength mechanisms should protect the primary authentication token (a cryptographic key) against compromise by the protocol threats, including eavesdropper, replay, online guessing, verifier impersonation, and man-in-the-middle attacks. A minimum of two authentication factors is required. Three kinds of tokens may be used:
   *
   *
   *                            "soft" cryptographic token, which has the key stored on a general-purpose computer,
   *                            "hard" cryptographic token, which has the key stored on a special hardware device, and
   *                            "one-time password" device token, which has symmetric key stored on a personal hardware device that is a cryptographic module validated at FIPS 140-2 Level 1 or higher. Validation testing of cryptographic modules and algorithms for conformance to Federal Information Processing Standard (FIPS) 140-2, Security Requirements for Cryptographic Modules, is managed by NIST.
   *
   *                         Authentication requires that the claimant prove control of the token through a secure authentication protocol. The token must be unlocked with a password or biometric representation, or a password must be used in a secure authentication protocol, to establish two-factor authentication. Long-term shared authentication secrets, if used, are never revealed to any party except the claimant and verifiers operated directly by the CSP; however, session (temporary) shared secrets may be provided to independent verifiers by the CSP. Approved cryptographic techniques are used for all operations.  Assertions issued about claimants as a result of a successful authentication are either cryptographically authenticated by relying parties (using approved methods) or are obtained directly from a trusted party via a secure authentication protocol.    [Summary of the technical requirements specified in NIST SP 800-63 for the four levels of assurance defined by the December 2003, the Office of Management and Budget (OMB) issued Memorandum M-04-04, E-Authentication Guidance for Federal Agencies.]
   */
  MediumAuthenticationLevelOfAssurance: v3_ObservationValue_MediumAuthenticationLevelOfAssurance,
  /**
   * Indicator of high digital quality or reliability of the digital reliability of the verification and validation process used to verify the claimed identity of an entity by securely associating an identifier and its authenticator. [Based on ISO 7498-2]
   *
   *                         The degree of confidence in the vetting process used to establish the identity of the individual to whom the credential was issued, and 2) the degree of confidence that the individual who uses the credential is the individual to whom the credential was issued. [OMB M-04-04 E-Authentication Guidance for Federal Agencies]
   *
   *                         High authentication level of assurance indicates that the relying party may have very high confidence in the asserted identity's validity. Level 4 is for transactions that need very high confidence in the accuracy of the asserted identity. Level 4 provides the highest practical assurance of remote network authentication. Authentication is based on proof of possession of a key through a cryptographic protocol. This level is similar to Level 3 except that only â€œhardâ€? cryptographic tokens are allowed, cryptographic module validation requirements are strengthened, and subsequent critical data transfers must be authenticated via a key that is bound to the authentication process. The token should be a hardware cryptographic module validated at FIPS 140-2 Level 2 or higher overall with at least FIPS 140-2 Level 3 physical security. This level requires a physical token, which cannot readily be copied, and operator authentication at Level 2 and higher, and ensures good, two-factor remote authentication.
   *
   *                         Level 4 requires strong cryptographic authentication of all parties and all sensitive data transfers between the parties. Either public key or symmetric key technology may be used. Authentication requires that the claimant prove through a secure authentication protocol that the claimant controls the token. Eavesdropper, replay, online guessing, verifier impersonation, and man-in-the-middle attacks are prevented. Long-term shared authentication secrets, if used, are never revealed to any party except the claimant and verifiers operated directly by the CSP; however, session (temporary) shared secrets may be provided to independent verifiers by the CSP. Strong approved cryptographic techniques are used for all operations. All sensitive data transfers are cryptographically authenticated using keys bound to the authentication process.   [Summary of the technical requirements specified in NIST SP 800-63 for the four levels of assurance defined by the December 2003, the Office of Management and Budget (OMB) issued Memorandum M-04-04, E-Authentication Guidance for Federal Agencies.]
   */
  HighAuthenticationLevelOfAssurance: v3_ObservationValue_HighAuthenticationLevelOfAssurance,
  /**
   * The value assigned as the indicator of the digital quality or reliability of a defined sequence of messages between a Claimant and a Verifier that demonstrates that the Claimant has possession and control of a valid token to establish his/her identity, and optionally, demonstrates to the Claimant that he or she is communicating with the intended Verifier. [Based on NIST SP 800-63-2]
   */
  AuthenticationProcessLevelOfAssuranceValue: v3_ObservationValue_AuthenticationProcessLevelOfAssuranceValue,
  /**
   * Indicator of the low digital quality or reliability of a defined sequence of messages between a Claimant and a Verifier that demonstrates that the Claimant has possession and control of a valid token to establish his/her identity, and optionally, demonstrates to the Claimant that he or she is communicating with the intended Verifier. [Based on NIST SP 800-63-2]
   *
   *                         Low authentication process level of assurance indicates that (1) long-term shared authentication secrets may be revealed to verifiers; and (2) assertions and assertion references require protection from manufacture/modification and reuse attacks.  [Summary of the technical requirements specified in NIST SP 800-63 for the four levels of assurance defined by the December 2003, the Office of Management and Budget (OMB) issued Memorandum M-04-04, E-Authentication Guidance for Federal Agencies.]
   */
  LowAuthenticationProcessLevelOfAssurance: v3_ObservationValue_LowAuthenticationProcessLevelOfAssurance,
  /**
   * Indicator of the basic digital quality or reliability of a defined sequence of messages between a Claimant and a Verifier that demonstrates that the Claimant has possession and control of a valid token to establish his/her identity, and optionally, demonstrates to the Claimant that he or she is communicating with the intended Verifier. [Based on NIST SP 800-63-2]
   *
   *                         Basic authentication process level of assurance indicates that long-term shared authentication secrets are never revealed to any other party except Credential Service Provider (CSP).  Sessions (temporary) shared secrets may be provided to independent verifiers by CSP. Long-term shared authentication secrets, if used, are never revealed to any other party except Verifiers operated by the Credential Service Provider (CSP); however, session (temporary) shared secrets may be provided to independent Verifiers by the CSP. In addition to Level 1 requirements, assertions are resistant to disclosure, redirection, capture and substitution attacks. Approved cryptographic techniques are required.  [Summary of the technical requirements specified in NIST SP 800-63 for the four levels of assurance defined by the December 2003, the Office of Management and Budget (OMB) issued Memorandum M-04-04, E-Authentication Guidance for Federal Agencies.]
   */
  BasicAuthenticationProcessLevelOfAssurance: v3_ObservationValue_BasicAuthenticationProcessLevelOfAssurance,
  /**
   * Indicator of the medium digital quality or reliability of a defined sequence of messages between a Claimant and a Verifier that demonstrates that the Claimant has possession and control of a valid token to establish his/her identity, and optionally, demonstrates to the Claimant that he or she is communicating with the intended Verifier. [Based on NIST SP 800-63-2]
   *
   *                         Medium authentication process level of assurance indicates that the token can be unlocked with password, biometric, or uses a secure multi-token authentication protocol to establish two-factor authentication.  Long-term shared authentication secrets are never revealed to any party except the Claimant and Credential Service Provider (CSP).
   *
   *                         Authentication requires that the Claimant prove, through a secure authentication protocol, that he or she controls the token. The Claimant unlocks the token with a password or biometric, or uses a secure multi-token authentication protocol to establish two-factor authentication (through proof of possession of a physical or software token in combination with some memorized secret knowledge). Long-term shared authentication secrets, if used, are never revealed to any party except the Claimant and Verifiers operated directly by the CSP; however, session (temporary) shared secrets may be provided to independent Verifiers by the CSP. In addition to Level 2 requirements, assertions are protected against repudiation by the Verifier.
   */
  MediumAuthenticationProcessLevelOfAssurance: v3_ObservationValue_MediumAuthenticationProcessLevelOfAssurance,
  /**
   * Indicator of the high digital quality or reliability of a defined sequence of messages between a Claimant and a Verifier that demonstrates that the Claimant has possession and control of a valid token to establish his/her identity, and optionally, demonstrates to the Claimant that he or she is communicating with the intended Verifier. [Based on NIST SP 800-63-2]
   *
   *                         High authentication process level of assurance indicates all sensitive data transfer are cryptographically authenticated using keys bound to the authentication process.  Level 4 requires strong cryptographic authentication of all communicating parties and all sensitive data transfers between the parties. Either public key or symmetric key technology may be used. Authentication requires that the Claimant prove through a secure authentication protocol that he or she controls the token. All protocol threats at Level 3 are required to be prevented at Level 4. Protocols shall also be strongly resistant to man-in-the-middle attacks. Long-term shared authentication secrets, if used, are never revealed to any party except the Claimant and Verifiers operated directly by the CSP; however, session (temporary) shared secrets may be provided to independent Verifiers by the CSP. Approved cryptographic techniques are used for all operations. All sensitive data transfers are cryptographically authenticated using keys bound to the authentication process.   [Summary of the technical requirements specified in NIST SP 800-63 for the four levels of assurance defined by the December 2003, the Office of Management and Budget (OMB) issued Memorandum M-04-04, E-Authentication Guidance for Federal Agencies.]
   */
  HighAuthenticationProcessLevelOfAssurance: v3_ObservationValue_HighAuthenticationProcessLevelOfAssurance,
  /**
   * The value assigned as the indicator of the high quality or reliability of the statement from a Verifier to a Relying Party (RP) that contains identity information about a Subscriber. Assertions may also contain verified attributes.
   */
  AssertionLevelOfAssuranceValue: v3_ObservationValue_AssertionLevelOfAssuranceValue,
  /**
   * Indicator of the low quality or reliability of the statement from a Verifier to a Relying Party (RP) that contains identity information about a Subscriber. Assertions may also contain verified attributes.
   *
   *                         Assertions and assertion references require protection from modification and reuse attacks.  [Summary of the technical requirements specified in NIST SP 800-63 for the four levels of assurance defined by the December 2003, the Office of Management and Budget (OMB) issued Memorandum M-04-04, E-Authentication Guidance for Federal Agencies.]
   */
  LowAssertionLevelOfAssurance: v3_ObservationValue_LowAssertionLevelOfAssurance,
  /**
   * Indicator of the basic quality or reliability of the statement from a Verifier to a Relying Party (RP) that contains identity information about a Subscriber. Assertions may also contain verified attributes.
   *
   *                         Assertions are resistant to disclosure, redirection, capture and substitution attacks.  Approved cryptographic techniques are required for all assertion protocols.  [Summary of the technical requirements specified in NIST SP 800-63 for the four levels of assurance defined by the December 2003, the Office of Management and Budget (OMB) issued Memorandum M-04-04, E-Authentication Guidance for Federal Agencies.]
   */
  BasicAssertionLevelOfAssurance: v3_ObservationValue_BasicAssertionLevelOfAssurance,
  /**
   * Indicator of the medium quality or reliability of the statement from a Verifier to a Relying Party (RP) that contains identity information about a Subscriber. Assertions may also contain verified attributes.
   *
   *                         Assertions are protected against repudiation by the verifier.  [Summary of the technical requirements specified in NIST SP 800-63 for the four levels of assurance defined by the December 2003, the Office of Management and Budget (OMB) issued Memorandum M-04-04, E-Authentication Guidance for Federal Agencies.]
   */
  MediumAssertionLevelOfAssurance: v3_ObservationValue_MediumAssertionLevelOfAssurance,
  /**
   * Indicator of the high quality or reliability of the statement from a Verifier to a Relying Party (RP) that contains identity information about a Subscriber. Assertions may also contain verified attributes.
   *
   *                         Strongly resistant to man-in-the-middle attacks. "Bearer" assertions are not used.  "Holder-of-key" assertions may be used. RP maintains records of the assertions.  [Summary of the technical requirements specified in NIST SP 800-63 for the four levels of assurance defined by the December 2003, the Office of Management and Budget (OMB) issued Memorandum M-04-04, E-Authentication Guidance for Federal Agencies.]
   */
  HighAssertionLevelOfAssurance: v3_ObservationValue_HighAssertionLevelOfAssurance,
  /**
   * Indicator of the digital quality or reliability of the activities performed by the Credential Service Provider (CSP) subsequent to electronic authentication registration, identity proofing and issuance activities to manage and safeguard the integrity of an issued credential and its binding to an identity. [Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  TokenAndCredentialManagementLevelOfAssuranceValue: v3_ObservationValue_TokenAndCredentialManagementLevelOfAssuranceValue,
  /**
   * Indicator of the low digital quality or reliability of the activities performed by the Credential Service Provider (CSP) subsequent to electronic authentication registration, identity proofing and issuance activities to manage and safeguard the integrity of an issued credential and its binding to an identity. Little or no confidence that an individual has maintained control over a token that has been entrusted to him or her and that that token has not been compromised. Characteristics include weak identity binding to tokens and plaintext passwords or secrets not transmitted across a network. [Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  LowTokenAndCredentialManagementLevelOfAssurance: v3_ObservationValue_LowTokenAndCredentialManagementLevelOfAssurance,
  /**
   * Indicator of the basic digital quality or reliability of the activities performed by the Credential Service Provider (CSP) subsequent to electronic authentication registration, identity proofing and issuance activities to manage and safeguard the integrity of an issued credential and its binding to an identity.  Some confidence that an individual has maintained control over a token that has been entrusted to him or her and that that token has not been compromised. Characteristics include:  Verification must prove claimant controls the token; token resists online guessing, replay, session hijacking, and eavesdropping attacks; and  token is at least weakly resistant to man-in-the middle attacks. [Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  BasicTokenAndCredentialManagementLevelOfAssurance: v3_ObservationValue_BasicTokenAndCredentialManagementLevelOfAssurance,
  /**
   * Indicator of the medium digital quality or reliability of the activities performed by the Credential Service Provider (CSP) subsequent to electronic authentication registration, identity proofing and issuance activities to manage and safeguard the integrity of an issued credential and itâ€™s binding to an identity.  High confidence that an individual has maintained control over a token that has been entrusted to him or her and that that token has not been compromised. Characteristics  include: Ownership of token verifiable through security authentication protocol and credential management protects against verifier impersonation attacks. [Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  MediumTokenAndCredentialManagementLevelOfAssurance: v3_ObservationValue_MediumTokenAndCredentialManagementLevelOfAssurance,
  /**
   * Indicator of the high digital quality or reliability of the activities performed by the Credential Service Provider (CSP) subsequent to electronic authentication registration, identity proofing and issuance activities to manage and safeguard the integrity of an issued credential and itâ€™s binding to an identity.  Very high confidence that an individual has maintained control over a token that has been entrusted to him or her and that that token has not been compromised. Characteristics include: Verifier can prove control of token through a secure protocol; credential management supports strong cryptographic authentication of all communication parties. [Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  HighTokenAndCredentialManagementLevelOfAssurance: v3_ObservationValue_HighTokenAndCredentialManagementLevelOfAssurance,
  /**
   * Indicator of the quality or reliability in the process of ascertaining that an individual is who he or she claims to be.
   */
  IdentityProofingLevelOfAssurance: v3_ObservationValue_IdentityProofingLevelOfAssurance,
  /**
   * Indicator of low digital quality or reliability in the process of ascertaining that an individual is who he or she claims to be.  Requires that a continuity of identity be maintained but does not require identity proofing. [Based on Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  LowIdentityProofingLevelOfAssurance: v3_ObservationValue_LowIdentityProofingLevelOfAssurance,
  /**
   * Indicator of some digital quality or reliability in the process of ascertaining that that an individual is who he or she claims to be. Requires identity proofing via presentation of identifying material or information. [Based on Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  BasicIdentityProofingLevelOfAssurance: v3_ObservationValue_BasicIdentityProofingLevelOfAssurance,
  /**
   * Indicator of high digital quality or reliability in the process of ascertaining that an individual is who he or she claims to be.  Requires identity proofing procedures for verification of identifying materials and information. [Based on Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  MediumIdentityProofingLevelOfAssurance: v3_ObservationValue_MediumIdentityProofingLevelOfAssurance,
  /**
   * Indicator of high digital quality or reliability in the process of ascertaining that an individual is who he or she claims to be.  Requires identity proofing procedures for verification of identifying materials and information. [Based on Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  HighIdentityProofingLevelOfAssurance: v3_ObservationValue_HighIdentityProofingLevelOfAssurance,
  /**
   * Indicator of the digital quality or reliability in the process of establishing proof of delivery and proof of origin. [Based on ISO 7498-2]
   */
  NonRepudiationLevelOfAssuranceValue: v3_ObservationValue_NonRepudiationLevelOfAssuranceValue,
  /**
   * Indicator of low digital quality or reliability in the process of establishing proof of delivery and proof of origin. [Based on ISO 7498-2]
   */
  LowNonRepudiationLevelOfAssurance: v3_ObservationValue_LowNonRepudiationLevelOfAssurance,
  /**
   * Indicator of basic digital quality or reliability in the process of establishing proof of delivery and proof of origin. [Based on ISO 7498-2]
   */
  BasicNonRepudiationLevelOfAssurance: v3_ObservationValue_BasicNonRepudiationLevelOfAssurance,
  /**
   * Indicator of medium digital quality or reliability in the process of establishing proof of delivery and proof of origin. [Based on ISO 7498-2]
   */
  MediumNonRepudiationLevelOfAssurance: v3_ObservationValue_MediumNonRepudiationLevelOfAssurance,
  /**
   * Indicator of high digital quality or reliability in the process of establishing proof of delivery and proof of origin. [Based on ISO 7498-2]
   */
  HighNonRepudiationLevelOfAssurance: v3_ObservationValue_HighNonRepudiationLevelOfAssurance,
  /**
   * Indicator of the digital quality or reliability of the information exchange between network-connected devices where the information cannot be reliably protected end-to-end by a single organizationâ€™s security controls. [Based on NIST SP 800-63-2]
   */
  RemoteAccessLevelOfAssuranceValue: v3_ObservationValue_RemoteAccessLevelOfAssuranceValue,
  /**
   * Indicator of low digital quality or reliability of the information exchange between network-connected devices where the information cannot be reliably protected end-to-end by a single organizationâ€™s security controls. [Based on NIST SP 800-63-2]
   */
  LowRemoteAccessLevelOfAssurance: v3_ObservationValue_LowRemoteAccessLevelOfAssurance,
  /**
   * Indicator of basic digital quality or reliability of the information exchange between network-connected devices where the information cannot be reliably protected end-to-end by a single organizationâ€™s security controls. [Based on NIST SP 800-63-2]
   */
  BasicRemoteAccessLevelOfAssurance: v3_ObservationValue_BasicRemoteAccessLevelOfAssurance,
  /**
   * Indicator of medium digital quality or reliability of the information exchange between network-connected devices where the information cannot be reliably protected end-to-end by a single organizationâ€™s security controls. [Based on NIST SP 800-63-2]
   */
  MediumRemoteAccessLevelOfAssurance: v3_ObservationValue_MediumRemoteAccessLevelOfAssurance,
  /**
   * Indicator of high digital quality or reliability of the information exchange between network-connected devices where the information cannot be reliably protected end-to-end by a single organization's security controls. [Based on NIST SP 800-63-2]
   */
  HighRemoteAccessLevelOfAssurance: v3_ObservationValue_HighRemoteAccessLevelOfAssurance,
  /**
   * Indicator of the digital quality or reliability of single and multi-token authentication. [Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  TokenLevelOfAssuranceValue: v3_ObservationValue_TokenLevelOfAssuranceValue,
  /**
   * Indicator of the low digital quality or reliability of single and multi-token authentication. Permits the use of any of the token methods of Levels 2, 3, or 4. [Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  LowTokenLevelOfAssurance: v3_ObservationValue_LowTokenLevelOfAssurance,
  /**
   * Indicator of the basic digital quality or reliability of single and multi-token authentication. Requires single factor authentication using memorized secret tokens, pre-registered knowledge tokens, look-up secret tokens, out of band tokens, or single factor one-time password devices. [Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  BasicTokenLevelOfAssurance: v3_ObservationValue_BasicTokenLevelOfAssurance,
  /**
   * Indicator of the medium digital quality or reliability of single and multi-token authentication. Requires two authentication factors. Provides multi-factor remote network authentication. Permits multi-factor software cryptographic token. [Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  MediumTokenLevelOfAssurance: v3_ObservationValue_MediumTokenLevelOfAssurance,
  /**
   * Indicator of the high digital quality or reliability of single and multi-token authentication. Requires token that is a hardware cryptographic module validated at validated at Federal Information Processing Standard (FIPS) 140-2 Level 2 or higher overall with at least FIPS 140-2 Level 3 physical security. Level 4 token requirements can be met by using the PIV authentication key of a FIPS 201 compliant Personal Identity Verification (PIV) Card.  [Electronic Authentication Guideline - Recommendations of the National Institute of Standards and Technology, NIST Special Publication 800-63-1, Dec 2011]
   */
  HighTokenLevelOfAssurance: v3_ObservationValue_HighTokenLevelOfAssurance,
  /**
   * Indicates the condition may result in noticable adverse adverse consequences but is unlikely to be life-threatening or cause permanent injury.
   */
  Moderate: v3_ObservationValue_Moderate,
  /**
   * Security metadata observation value used to indicate that the IT resource semantic content has been transformed from one encoding to another.
   *
   *
   *                            Usage Note: "MAP" code does not indicate the semantic fidelity of the transformed content.
   *
   *                         To indicate semantic fidelity for maps of HL7 to other code systems, this security alteration integrity observation may be further specified using an Act valued with Value Set: MapRelationship (2.16.840.1.113883.1.11.11052).
   *
   *                         Semantic fidelity of the mapped IT Resource may also be indicated using a SecurityIntegrityConfidenceObservation.
   */
  Mapped: v3_ObservationValue_Mapped,
  /**
   * Security metadata observation value conveying the alteration integrity of an IT resource (data, information object, service, or system capability) by indicating the mechanism by which software systems can make data unintelligible (that is, as unreadable and unusable by algorithmically transforming plaintext into ciphertext) such that it can only be accessed or used by authorized users.  An authorized user may be provided a key to decrypt per license or "shared secret".
   *
   *
   *                            Usage Note: "MASKED" may be used, per applicable policy, as a flag to indicate to a user or receiver that some portion of an IT resource has been further encrypted, and may be accessed only by an authorized user or receiver to which a decryption key is provided.
   */
  Masked: v3_ObservationValue_Masked,
  /**
   * Value for Act.partialCompletionCode attribute that implies 41-60% completion
   */
  MediumExtent: v3_ObservationValue_MediumExtent,
  /**
   * Indicator of status as covered member under a policy or program, e.g., member id card or coverage document.
   */
  ProgramOrPolicyMember: v3_ObservationValue_ProgramOrPolicyMember,
  /**
   * Value for Act.partialCompletionCode attribute that implies 1-20% completion
   */
  MinimalExtent: v3_ObservationValue_MinimalExtent,
  /**
   * Indicator of military status.
   */
  MilitaryIdentification: v3_ObservationValue_MilitaryIdentification,
  /**
   * Indicator of mortgage amount, interest, and payments.
   */
  Mortgage: v3_ObservationValue_Mortgage,
  /**
   * Indicator of marriage status.
   */
  MarriageCertificate: v3_ObservationValue_MarriageCertificate,
  /**
   * Value for Act.partialCompletionCode attribute that implies 0% completion
   */
  None: v3_ObservationValue_None,
  /**
   * Definition: Coverage is not in effect for healthcare service(s) and/or product(s). May optionally include reasons for the ineligibility.
   */
  NotEligible: v3_ObservationValue_NotEligible,
  /**
   * Persons not classified as employed or unemployed, meaning those who have no job and are not looking for one.
   */
  NotInLaborForce: v3_ObservationValue_NotInLaborForce,
  /**
   * Scheduled for work during nighttime hours (for example between 9pm and 8am) on a regular basis.
   */
  NightShift: v3_ObservationValue_NightShift,
  /**
   * Code specifying that the measure uses opportunity-based scoring. In opportunity-based scoring the measure score is determined by combining the denominator and numerator of each component measure to determine an overall composite score.
   */
  OpportunityScoring: v3_ObservationValue_OpportunityScoring,
  /**
   * A measure that indicates the result of the performance (or non-performance) of a function or process.
   */
  OutcomeMeasureType: v3_ObservationValue_OutcomeMeasureType,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a patient acquaintance.
   */
  PatientAcquaintanceAsserted: v3_ObservationValue_PatientAcquaintanceAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a patient acquaintance.
   */
  PatientAcquaintanceReported: v3_ObservationValue_PatientAcquaintanceReported,
  /**
   * Indicator of citizenship.
   */
  Passport: v3_ObservationValue_Passport,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a patient.
   */
  PatientAsserted: v3_ObservationValue_PatientAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a patient.
   */
  PatientReported: v3_ObservationValue_PatientReported,
  /**
   * Indicator of paid employment, e.g., letter of hire, contract, employer letter; copy of pay check or pay stub.
   */
  PaidEmployment: v3_ObservationValue_PaidEmployment,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a payer.
   */
  PayerAsserted: v3_ObservationValue_PayerAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a payer.
   */
  PayerReported: v3_ObservationValue_PayerReported,
  /**
   * Non-clinical report of pregnancy.
   */
  Pregnant: v3_ObservationValue_Pregnant,
  /**
   * Lying with the front or ventral surface downward; lying face down.
   */
  Prone: v3_ObservationValue_Prone,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a professional.
   */
  ProfessionalAsserted: v3_ObservationValue_ProfessionalAsserted,
  /**
   * Indication of probation status.
   */
  Probation: v3_ObservationValue_Probation,
  /**
   * A measure which focuses on a process which leads to a certain outcome, meaning that a scientific basis exists for believing that the process, when executed well, will increase the probability of achieving a desired outcome.
   */
  ProcessMeasureType: v3_ObservationValue_ProcessMeasureType,
  /**
   * Indicator of real property ownership, e.g., deed or real estate contract.
   */
  RealProperty: v3_ObservationValue_RealProperty,
  /**
   * A measure that is a comparison of patient reported outcomes for a single or multiple patients collected via an instrument specifically designed to obtain input directly from patients.
   */
  IntermediateClinicalOutcomeMeasure_2: v3_ObservationValue_IntermediateClinicalOutcomeMeasure_2,
  /**
   * A score derived by dividing the number of cases that meet a criterion for quality (the numerator) by the number of eligible cases within a given time frame (the denominator) where the numerator cases are a subset of the denominator cases (e.g., percentage of eligible women with a mammogram performed in the last year).
   */
  ProportionMeasureScoring: v3_ObservationValue_ProportionMeasureScoring,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a professional.
   */
  ProfessionalReported: v3_ObservationValue_ProfessionalReported,
  /**
   * Security metadata observation value conveying the alteration integrity of an IT resource (data, information object, service, or system capability), by indicating the mechanism by which software systems can strip portions of the resource that could allow the identification of the source of the information or the information subject.  Custodian may retain a key to relink data necessary to reidentify the information subject.
   *
   *
   *                            Rationale: Personal data which has been processed to make it impossible to know whose data it is. Used particularly for secondary use of health data. In some cases, it may be possible for authorized individuals to restore the identity of the individual, e.g.,for public health case management.  Based on ISO/TS 25237:2008 Health informaticsâ€”Pseudonymization
   */
  Pseudonymized: v3_ObservationValue_Pseudonymized,
  /**
   * A score that may have a value of zero or greater that is derived by dividing a count of one type of data by a count of another type of data (e.g., the number of patients with central lines who develop infection divided by the number of central line days).
   */
  RatioMeasureScoring: v3_ObservationValue_RatioMeasureScoring,
  /**
   * Security metadata observation value used to indicate the mechanism by which software systems can filter an IT resource (data, information object, service, or system capability) to remove any portion of the resource that is not authorized to be access, used, or disclosed.
   *
   *
   *                            Usage Note: "REDACTED" may be used, per applicable policy, as a flag to indicate to a user or receiver that some portion of an IT resource has filtered and not included in the content accessed or received.
   */
  Redacted: v3_ObservationValue_Redacted,
  /**
   * Indication of refugee status.
   */
  Refugee: v3_ObservationValue_Refugee,
  /**
   * Living with one or more relatives. Maps to PD1-2   Living arrangement   (IS)   00742 [R]
   */
  Relative: v3_ObservationValue_Relative,
  /**
   * Continued living in private residence requires functional and health care assistance from one or more relatives.
   */
  RelativeDependent: v3_ObservationValue_RelativeDependent,
  /**
   * Security metadata observation value used to indicate that the veracity or trustworthiness of an IT resource (data, information object, service, or system capability) for a specified purpose of use is perceived to be or deemed by policy to be adequate.
   */
  Reliable: v3_ObservationValue_Reliable,
  /**
   * Indicator of rental or lease payments.
   */
  Rent: v3_ObservationValue_Rent,
  /**
   * A measure related to the extent of use of clinical resources or cost of care.
   */
  ResourceUseMeasureType: v3_ObservationValue_ResourceUseMeasureType,
  /**
   * Indicator of retirement investment account ownership.
   */
  RetirementInvestmentAccount: v3_ObservationValue_RetirementInvestmentAccount,
  /**
   * Indicator of retirement payment, e.g., pension check.
   */
  RetirementPay: v3_ObservationValue_RetirementPay,
  /**
   * Lying on the right side.
   */
  RightLateralDecubitus: v3_ObservationValue_RightLateralDecubitus,
  /**
   * Scheduled for work times that change periodically between days, and/or evenings, and includes some night shifts.
   */
  RotatingShiftWithNights: v3_ObservationValue_RotatingShiftWithNights,
  /**
   * Scheduled for work days/times that change periodically between days, but does not include night or evening work.
   */
  RotatingShiftWithoutNights: v3_ObservationValue_RotatingShiftWithoutNights,
  /**
   * Lying on the back, on an inclined plane, typically about 30-45 degrees with head raised and feet lowered.
   */
  ReverseTrendelenburg: v3_ObservationValue_ReverseTrendelenburg,
  /**
   * Value for Act.partialCompletionCode attribute that implies 21-40% completion
   */
  SomeExtent: v3_ObservationValue_SomeExtent,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a substitute decision maker.
   */
  SubstituteDecisionMakerAsserted: v3_ObservationValue_SubstituteDecisionMakerAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a substitute decision maker.
   */
  SubstituteDecisionMakerReported: v3_ObservationValue_SubstituteDecisionMakerReported,
  /**
   * Observation value used to indicate aspects of trust applicable to an IT resource (data, information object, service, or system capability).
   */
  SecurityTrustObservation: v3_ObservationValue_SecurityTrustObservation,
  /**
   * A semi-sitting position in bed with the head of the bed elevated approximately 45 degrees.
   */
  SemiFowlerQuoteS: v3_ObservationValue_SemiFowlerQuoteS,
  /**
   * Resting the body on the buttocks, typically with upper torso erect or semi erect.
   */
  Sitting: v3_ObservationValue_Sitting,
  /**
   * Code specifying observations or indicators related to socio-economic status used to assess to assess for services, e.g., discharge planning, or to establish eligibility for coverage under a policy or program.
   */
  SocioEconomicStatus: v3_ObservationValue_SocioEconomicStatus,
  /**
   * Indicator of spousal or partner support payments received or provided; e.g., alimony payment; support stipulations in a divorce settlement.
   */
  SpousalOrPartnerSupport: v3_ObservationValue_SpousalOrPartnerSupport,
  /**
   * Living only with spouse or life partner. Maps to PD1-2   Living arrangement   (IS)   00742 [S]
   */
  SpouseOnly: v3_ObservationValue_SpouseOnly,
  /**
   * Continued living in private residence requires functional and health care assistance from spouse or life partner.
   */
  SpouseDependent: v3_ObservationValue_SpouseDependent,
  /**
   * Shift consisting of two distinct work periods each day that are separated by a break of a few hours (for example 2 to 4 hours)
   */
  SplitShift: v3_ObservationValue_SplitShift,
  /**
   * Description:A pair of people of the same gender who live together as a family as specified by coverage policy or program, e.g., Naomi and Ruth from the Book of Ruth; Socrates and Alcibiades
   */
  SameSexPartner: v3_ObservationValue_SameSexPartner,
  /**
   * To be stationary, upright, vertical, on one's legs.
   */
  Standing: v3_ObservationValue_Standing,
  /**
   * A measure related to the structure of patient care.
   */
  StructureMeasureType: v3_ObservationValue_StructureMeasureType,
  /**
   * Indicator of student status.
   */
  StudentEnrollment: v3_ObservationValue_StudentEnrollment,
  /**
   * Metadata observation used to indicate that some information has been removed from the source object when the view this object contains was constructed because of configuration options when the view was created. The content may not be suitable for use as the basis of a record update
   *
   *
   *                            Usage Note: This is not suitable to be used when information is removed for security reasons - see the code REDACTED for this use.
   */
  Subsetted: v3_ObservationValue_Subsetted,
  /**
   * Indicator of transportation expenses.
   */
  SundryExpense: v3_ObservationValue_SundryExpense,
  /**
   * supine
   */
  Supine: v3_ObservationValue_Supine,
  /**
   * Indicator of income supplement, e.g., gifting, parental income support; stipend, or grant.
   */
  IncomeSupplement: v3_ObservationValue_IncomeSupplement,
  /**
   * Security metadata observation value used to indicate that the IT resource syntax has been transformed from one syntactical representation to another.
   *
   *
   *                            Usage Note: "SYNTAC" code does not indicate the syntactical correctness of the syntactically transformed IT resource.
   */
  SyntacticTransform: v3_ObservationValue_SyntacticTransform,
  /**
   * Indicator of tax obligation or payment, e.g., statement of taxable income.
   */
  TaxObligation: v3_ObservationValue_TaxObligation,
  /**
   * Indicator of transportation expenses, e.g., vehicle payments, vehicle insurance, vehicle fuel, and vehicle maintenance and repairs.
   */
  TransportationExpense: v3_ObservationValue_TransportationExpense,
  /**
   * Lying on the back, on an inclined plane, typically about 30-45 degrees, with  head lowered and feet raised.
   */
  Trendelenburg: v3_ObservationValue_Trendelenburg,
  /**
   * Security metadata observation value used to indicate that the IT resource has been translated from one human language to another.
   *
   *
   *                            Usage Note: "TRSLT" does not indicate the fidelity of the translation or the languages translated.
   *
   *                         The fidelity of the IT Resource translation may be indicated using a SecurityIntegrityConfidenceObservation.
   *
   *                         To indicate languages, use the Value Set:HumanLanguage (2.16.840.1.113883.1.11.11526)
   */
  Translated: v3_ObservationValue_Translated,
  /**
   * Values for security trust accreditation metadata observation made about the formal declaration by an authority or neutral third party that validates the technical, security, trust, and business practice conformance of Trust Agents to facilitate security, interoperability, and trust among participants within a security domain or trust framework.
   */
  TrustAccreditationObservation: v3_ObservationValue_TrustAccreditationObservation,
  /**
   * Values for security trust agreement metadata observation made about privacy and security requirements with which a security domain must comply. [ISO IEC 10181-1]
   * [ISO IEC 10181-1]
   */
  TrustAgreementObservation: v3_ObservationValue_TrustAgreementObservation,
  /**
   * Values for security trust certificate metadata observation made about a set of security-relevant data issued by a security authority or trusted third party, together with security information which is used to provide the integrity and data origin authentication services for an IT resource (data, information object, service, or system capability). [Based on ISO IEC 10181-1]
   *
   *                         For example, a Certificate Policy (CP), which is a named set of rules that indicates the applicability of a certificate to a particular community and/or class of application with common security requirements.  A particular Certificate Policy might indicate the applicability of a type of certificate to the authentication of electronic data interchange transactions for the trading of goods within a given price range.  Another example is Cross Certification with Federal Bridge.
   */
  TrustCertificateObservation: v3_ObservationValue_TrustCertificateObservation,
  /**
   * Values for security trust assurance metadata observation made about the digital quality or reliability of a trust assertion, activity, capability, information exchange, mechanism, process, or protocol.
   */
  TrustAssuranceObservation: v3_ObservationValue_TrustAssuranceObservation,
  /**
   * Values for security trust mechanism metadata observation made about a security architecture system component that supports enforcement of security policies.
   */
  NoneSupplied6: v3_ObservationValue_NoneSupplied6,
  /**
   * Indicator of status as trust beneficiary.
   */
  Trust: v3_ObservationValue_Trust,
  /**
   * Security metadata observation value used to indicate that the veracity or trustworthiness of an IT resource (data, information object, service, or system capability) for a specified purpose of use is perceived to be or deemed by policy to be uncertain.
   */
  UncertainReliability: v3_ObservationValue_UncertainReliability,
  /**
   * Indication of unemployed status.
   */
  Unemployed: v3_ObservationValue_Unemployed_2,
  /**
   * Persons who currently have no employment, but are available for work and have made specific efforts to find employment.
   */
  Unemployed_2: v3_ObservationValue_Unemployed,
  /**
   * Living with one or more unrelated persons.
   */
  UnrelatedPerson: v3_ObservationValue_UnrelatedPerson,
  /**
   * Security metadata observation value used to indicate that the veracity or trustworthiness of an IT resource (data, information object, service, or system capability) for a specified purpose of use is perceived to be or deemed by policy to be inadequate.
   */
  Unreliable: v3_ObservationValue_Unreliable,
  /**
   * Continued living in private residence requires functional and health care assistance from one or more unrelated persons.
   */
  UnrelatedPersonDependent: v3_ObservationValue_UnrelatedPersonDependent,
  /**
   * Indicator of transportation expenses.
   */
  UtilityExpense: v3_ObservationValue_UtilityExpense,
  /**
   * Security metadata observation value conveying the alteration integrity of an IT resource (data, information object, service, or system capability)  which indicates that the resource only retains versions of an IT resource  for access and use per applicable policy
   *
   *
   *                            Usage Note: When this code is used, expectation is that the system has removed historical versions of the data that falls outside the time period deemed to be the effective time of the applicable version.
   */
  Versioned: v3_ObservationValue_Versioned,
  /**
   * Shifts of 17 or more hours.
   */
  VeryLongShift: v3_ObservationValue_VeryLongShift,
  /**
   * Irregular, unpredictable hours scheduled on a short notice (for example, less than 2 day notice): inconsistent schedule, on-call, as needed, as available.
   */
  VariableShift: v3_ObservationValue_VariableShift,
  /**
   * Code specifying that the measure uses weighted scoring. Weighted scoring assigns a factor to each component measure to weight that measure's contribution to the overall score.
   */
  WeightedScoring: v3_ObservationValue_WeightedScoring,
};
const v3_orderableDrugForm_AdministrableDrugForm: Coding = {
  code: '_AdministrableDrugForm',
  display: 'AdministrableDrugForm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_DispensableDrugForm: Coding = {
  code: '_DispensableDrugForm',
  display: 'DispensableDrugForm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_GasDrugForm: Coding = {
  code: '_GasDrugForm',
  display: 'GasDrugForm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_GasLiquidMixture: Coding = {
  code: '_GasLiquidMixture',
  display: 'GasLiquidMixture',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_GasSolidSpray: Coding = {
  code: '_GasSolidSpray',
  display: 'GasSolidSpray',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Liquid: Coding = {
  code: '_Liquid',
  display: 'Liquid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_LiquidLiquidEmulsion: Coding = {
  code: '_LiquidLiquidEmulsion',
  display: 'LiquidLiquidEmulsion',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_LiquidSolidSuspension: Coding = {
  code: '_LiquidSolidSuspension',
  display: 'LiquidSolidSuspension',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_SolidDrugForm: Coding = {
  code: '_SolidDrugForm',
  display: 'SolidDrugForm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Aerosol: Coding = {
  code: 'AER',
  display: 'Aerosol',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Applicatorful: Coding = {
  code: 'APPFUL',
  display: 'Applicatorful',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_BreathActivatedInhaler: Coding = {
  code: 'BAINHL',
  display: 'Breath Activated Inhaler',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_BreathActivatedPowderInhaler: Coding = {
  code: 'BAINHLPWD',
  display: 'Breath Activated Powder Inhaler',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Bar: Coding = {
  code: 'BAR',
  display: 'Bar',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_BarSoap: Coding = {
  code: 'BARSOAP',
  display: 'Bar Soap',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Beads: Coding = {
  code: 'BEAD',
  display: 'Beads',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_BuccalTablet: Coding = {
  code: 'BUCTAB',
  display: 'Buccal Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Cake: Coding = {
  code: 'CAKE',
  display: 'Cake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Capsule: Coding = {
  code: 'CAP',
  display: 'Capsule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Caplet: Coding = {
  code: 'CAPLET',
  display: 'Caplet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Cement: Coding = {
  code: 'CEMENT',
  display: 'Cement',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_ChewableBar: Coding = {
  code: 'CHEWBAR',
  display: 'Chewable Bar',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_ChewableTablet: Coding = {
  code: 'CHEWTAB',
  display: 'Chewable Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_CoatedParticlesTablet: Coding = {
  code: 'CPTAB',
  display: 'Coated Particles Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Cream: Coding = {
  code: 'CRM',
  display: 'Cream',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Crystals: Coding = {
  code: 'CRYS',
  display: 'Crystals',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_DermalSpray: Coding = {
  code: 'DERMSPRY',
  display: 'Dermal Spray',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_DisintegratingTablet: Coding = {
  code: 'DISINTAB',
  display: 'Disintegrating Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Disk: Coding = {
  code: 'DISK',
  display: 'Disk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Douche: Coding = {
  code: 'DOUCHE',
  display: 'Douche',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Drops: Coding = {
  code: 'DROP',
  display: 'Drops',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_DelayedReleaseTablet: Coding = {
  code: 'DRTAB',
  display: 'Delayed Release Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_EntericCoatedTablet: Coding = {
  code: 'ECTAB',
  display: 'Enteric Coated Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Elixir: Coding = {
  code: 'ELIXIR',
  display: 'Elixir',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Enema: Coding = {
  code: 'ENEMA',
  display: 'Enema',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_EntericCoatedCapsule: Coding = {
  code: 'ENTCAP',
  display: 'Enteric Coated Capsule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_ExtendedReleaseCapsule: Coding = {
  code: 'ERCAP',
  display: 'Extended Release Capsule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VAL12HourExtendedReleaseCapsule: Coding = {
  code: 'ERCAP12',
  display: '12 Hour Extended Release Capsule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VAL24HourExtendedReleaseCapsule: Coding = {
  code: 'ERCAP24',
  display: '24 Hour Extended Release Capsule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_ExtendedReleaseEntericCoatedCapsule: Coding = {
  code: 'ERECCAP',
  display: 'Extended Release Enteric Coated Capsule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_ExtendedReleaseEntericCoatedTablet: Coding = {
  code: 'ERECTAB',
  display: 'Extended Release Enteric Coated Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_ExtendedReleaseEntericCoatedCapsule_2: Coding = {
  code: 'ERENTCAP',
  display: 'Extended Release Enteric Coated Capsule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_ExtendedReleaseSuspension: Coding = {
  code: 'ERSUSP',
  display: 'Extended-Release Suspension',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VAL12HourExtendedReleaseSuspension: Coding = {
  code: 'ERSUSP12',
  display: '12 Hour Extended-Release Suspension',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VAL24HourExtendedReleaseSuspension: Coding = {
  code: 'ERSUSP24',
  display: '24 Hour Extended Release Suspension',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_ExtendedReleaseTablet: Coding = {
  code: 'ERTAB',
  display: 'Extended Release Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VAL12HourExtendedReleaseTablet: Coding = {
  code: 'ERTAB12',
  display: '12 Hour Extended Release Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VAL24HourExtendedReleaseTablet: Coding = {
  code: 'ERTAB24',
  display: '24 Hour Extended Release Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Flakes: Coding = {
  code: 'FLAKE',
  display: 'Flakes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Foam: Coding = {
  code: 'FOAM',
  display: 'Foam',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_FoamWithApplicator: Coding = {
  code: 'FOAMAPL',
  display: 'Foam with Applicator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_GasForInhalation: Coding = {
  code: 'GASINHL',
  display: 'Gas for Inhalation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Gel: Coding = {
  code: 'GEL',
  display: 'Gel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_GelWithApplicator: Coding = {
  code: 'GELAPL',
  display: 'Gel with Applicator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Granules: Coding = {
  code: 'GRAN',
  display: 'Granules',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_ChewingGum: Coding = {
  code: 'GUM',
  display: 'ChewingGum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Inhalant: Coding = {
  code: 'INHL',
  display: 'Inhalant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_InhalantPowder: Coding = {
  code: 'INHLPWD',
  display: 'Inhalant Powder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_InhalantSolution: Coding = {
  code: 'INHLSOL',
  display: 'Inhalant Solution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_IntraperitonealSolution: Coding = {
  code: 'IPSOL',
  display: 'Intraperitoneal Solution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_IrrigationSolution: Coding = {
  code: 'IRSOL',
  display: 'Irrigation Solution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_IntrathecalSuspension: Coding = {
  code: 'ITSUSP',
  display: 'Intrathecal Suspension',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_IntravenousSolution: Coding = {
  code: 'IVSOL',
  display: 'Intravenous Solution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Liniment: Coding = {
  code: 'LIN',
  display: 'Liniment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_LiquidCleanser: Coding = {
  code: 'LIQCLN',
  display: 'Liquid Cleanser',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_MedicatedLiquidSoap: Coding = {
  code: 'LIQSOAP',
  display: 'Medicated Liquid Soap',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Lotion: Coding = {
  code: 'LTN',
  display: 'Lotion',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_MeteredDoseInhaler: Coding = {
  code: 'MDINHL',
  display: 'Metered Dose Inhaler',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_MeteredDosePowderInhaler: Coding = {
  code: 'MDINHLPWD',
  display: 'Metered Dose Powder Inhaler',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_MedicatedBarSoap: Coding = {
  code: 'MEDBAR',
  display: 'Medicated Bar Soap',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_MedicatedPad: Coding = {
  code: 'MEDPAD',
  display: 'Medicated Pad',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_MedicatedSwab: Coding = {
  code: 'MEDSWAB',
  display: 'Medicated swab',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_MucousMembraneTopicalSolution: Coding = {
  code: 'MUCTOPSOL',
  display: 'Mucous Membrane Topical Solution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_NasalCream: Coding = {
  code: 'NASCRM',
  display: 'Nasal Cream',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_NasalGel: Coding = {
  code: 'NASGEL',
  display: 'Nasal Gel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_NasalInhalant: Coding = {
  code: 'NASINHL',
  display: 'Nasal Inhalant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_NasalOintment: Coding = {
  code: 'NASOINT',
  display: 'Nasal Ointment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_NasalSpray: Coding = {
  code: 'NASSPRY',
  display: 'Nasal Spray',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_NasalDrops: Coding = {
  code: 'NDROP',
  display: 'Nasal Drops',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Oil: Coding = {
  code: 'OIL',
  display: 'Oil',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Ointment: Coding = {
  code: 'OINT',
  display: 'Ointment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OintmentWithApplicator: Coding = {
  code: 'OINTAPL',
  display: 'Ointment with Applicator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OphthalmicCream: Coding = {
  code: 'OPCRM',
  display: 'Ophthalmic Cream',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OphthalmicDrops: Coding = {
  code: 'OPDROP',
  display: 'Ophthalmic Drops',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OphthalmicGel: Coding = {
  code: 'OPGEL',
  display: 'Ophthalmic Gel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OphthalmicIrrigationSolution: Coding = {
  code: 'OPIRSOL',
  display: 'Ophthalmic Irrigation Solution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OphthalmicOintment: Coding = {
  code: 'OPOINT',
  display: 'Ophthalmic Ointment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OphthalmicSuspension: Coding = {
  code: 'OPSUSP',
  display: 'Ophthalmic Suspension',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OralSolution: Coding = {
  code: 'ORALSOL',
  display: 'Oral Solution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OralCapsule: Coding = {
  code: 'ORCAP',
  display: 'Oral Capsule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OralCream: Coding = {
  code: 'ORCRM',
  display: 'Oral Cream',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OralDrops: Coding = {
  code: 'ORDROP',
  display: 'Oral Drops',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OralInhalant: Coding = {
  code: 'ORINHL',
  display: 'Oral Inhalant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OralSuspension: Coding = {
  code: 'ORSUSP',
  display: 'Oral Suspension',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OralTablet: Coding = {
  code: 'ORTAB',
  display: 'Oral Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_LozengeOralTroche: Coding = {
  code: 'ORTROCHE',
  display: 'Lozenge/Oral Troche',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OticCream: Coding = {
  code: 'OTCRM',
  display: 'Otic Cream',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OticDrops: Coding = {
  code: 'OTDROP',
  display: 'Otic Drops',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OticGel: Coding = {
  code: 'OTGEL',
  display: 'Otic Gel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OticOintment: Coding = {
  code: 'OTOINT',
  display: 'Otic Ointment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_OticSuspension: Coding = {
  code: 'OTSUSP',
  display: 'Otic Suspension',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Pad: Coding = {
  code: 'PAD',
  display: 'Pad',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Paste: Coding = {
  code: 'PASTE',
  display: 'Paste',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Patch: Coding = {
  code: 'PATCH',
  display: 'Patch',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Pellet: Coding = {
  code: 'PELLET',
  display: 'Pellet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Pill: Coding = {
  code: 'PILL',
  display: 'Pill',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Powder: Coding = {
  code: 'POWD',
  display: 'Powder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Pudding: Coding = {
  code: 'PUD',
  display: 'Pudding',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Puff: Coding = {
  code: 'PUFF',
  display: 'Puff',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_PowderSpray: Coding = {
  code: 'PWDSPRY',
  display: 'Powder Spray',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_RectalCream: Coding = {
  code: 'RECCRM',
  display: 'Rectal Cream',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_RectalFoam: Coding = {
  code: 'RECFORM',
  display: 'Rectal foam',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_RectalOintment: Coding = {
  code: 'RECOINT',
  display: 'Rectal Ointment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_RectalPowder: Coding = {
  code: 'RECPWD',
  display: 'Rectal Powder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_RectalSolution: Coding = {
  code: 'RECSOL',
  display: 'Rectal Solution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_RectalSpray: Coding = {
  code: 'RECSPRY',
  display: 'Rectal Spray',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_RectalSuppository: Coding = {
  code: 'RECSUPP',
  display: 'Rectal Suppository',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_RectalSuspension: Coding = {
  code: 'RECSUSP',
  display: 'Rectal Suspension',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_MouthwashRinse: Coding = {
  code: 'RINSE',
  display: 'Mouthwash/Rinse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Scoops: Coding = {
  code: 'SCOOP',
  display: 'Scoops',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Shampoo: Coding = {
  code: 'SHMP',
  display: 'Shampoo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_SublingualTablet: Coding = {
  code: 'SLTAB',
  display: 'Sublingual Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Solution: Coding = {
  code: 'SOL',
  display: 'Solution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Sprays: Coding = {
  code: 'SPRY',
  display: 'Sprays',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_SprayWithAdaptor: Coding = {
  code: 'SPRYADAPT',
  display: 'Spray with Adaptor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_SustainedReleaseBuccalTablet: Coding = {
  code: 'SRBUCTAB',
  display: 'Sustained Release Buccal Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Suppository: Coding = {
  code: 'SUPP',
  display: 'Suppository',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Suspension: Coding = {
  code: 'SUSP',
  display: 'Suspension',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Swab: Coding = {
  code: 'SWAB',
  display: 'Swab',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Syrup: Coding = {
  code: 'SYRUP',
  display: 'Syrup',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Tablet: Coding = {
  code: 'TAB',
  display: 'Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Tincture: Coding = {
  code: 'TINC',
  display: 'Tincture',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_TopicalCream: Coding = {
  code: 'TOPCRM',
  display: 'Topical Cream',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_TopicalGel: Coding = {
  code: 'TOPGEL',
  display: 'Topical Gel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_TopicalLotion: Coding = {
  code: 'TOPLTN',
  display: 'Topical Lotion',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_TopicalOil: Coding = {
  code: 'TOPOIL',
  display: 'Topical Oil',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_TopicalOintment: Coding = {
  code: 'TOPOINT',
  display: 'Topical Ointment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_TopicalPowder: Coding = {
  code: 'TOPPWD',
  display: 'Topical Powder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_TopicalSolution: Coding = {
  code: 'TOPSOL',
  display: 'Topical Solution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Toothpaste: Coding = {
  code: 'TPASTE',
  display: 'Toothpaste',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_TransdermalPatch: Coding = {
  code: 'TPATCH',
  display: 'Transdermal Patch',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VAL16HourTransdermalPatch: Coding = {
  code: 'TPATH16',
  display: '16 Hour Transdermal Patch',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VAL24HourTransdermalPatch: Coding = {
  code: 'TPATH24',
  display: '24 Hour Transdermal Patch',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_BiweeklyTransdermalPatch: Coding = {
  code: 'TPATH2WK',
  display: 'Biweekly Transdermal Patch',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VAL72HourTransdermalPatch: Coding = {
  code: 'TPATH72',
  display: '72 Hour Transdermal Patch',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_WeeklyTransdermalPatch: Coding = {
  code: 'TPATHWK',
  display: 'Weekly Transdermal Patch',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_UrethralGel: Coding = {
  code: 'URETHGEL',
  display: 'Urethral Gel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_UrethralSuppository: Coding = {
  code: 'URETHSUPP',
  display: 'Urethral suppository',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VaginalCream: Coding = {
  code: 'VAGCRM',
  display: 'Vaginal Cream',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VaginalCreamWithApplicator: Coding = {
  code: 'VAGCRMAPL',
  display: 'Vaginal Cream with Applicator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VaginalFoam: Coding = {
  code: 'VAGFOAM',
  display: 'Vaginal foam',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VaginalFoamWithApplicator: Coding = {
  code: 'VAGFOAMAPL',
  display: 'Vaginal foam with applicator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VaginalGel: Coding = {
  code: 'VAGGEL',
  display: 'Vaginal Gel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VaginalOintment: Coding = {
  code: 'VAGOINT',
  display: 'Vaginal Ointment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VaginalOintmentWithApplicator: Coding = {
  code: 'VAGOINTAPL',
  display: 'Vaginal Ointment with Applicator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VaginalPowder: Coding = {
  code: 'VAGPWD',
  display: 'Vaginal Powder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VaginalSpray: Coding = {
  code: 'VAGSPRY',
  display: 'Vaginal Spray',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VaginalSuppository: Coding = {
  code: 'VAGSUPP',
  display: 'Vaginal Suppository',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VaginalTablet: Coding = {
  code: 'VAGTAB',
  display: 'Vaginal Tablet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_VaginalGelWithApplicator: Coding = {
  code: 'VGELAPL',
  display: 'Vaginal Gel with Applicator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
const v3_orderableDrugForm_Wafer: Coding = {
  code: 'WAFER',
  display: 'Wafer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-orderableDrugForm',
};
/**
 *   OpenIssue:
 * Missing description.
 */
export const V3OrderableDrugForm = {
  /**
   * AdministrableDrugForm
   */
  AdministrableDrugForm: v3_orderableDrugForm_AdministrableDrugForm,
  /**
   * DispensableDrugForm
   */
  DispensableDrugForm: v3_orderableDrugForm_DispensableDrugForm,
  /**
   * Any elastic aeriform fluid in which the molecules are separated from one another and have free paths.
   */
  GasDrugForm: v3_orderableDrugForm_GasDrugForm,
  /**
   * GasLiquidMixture
   */
  GasLiquidMixture: v3_orderableDrugForm_GasLiquidMixture,
  /**
   * GasSolidSpray
   */
  GasSolidSpray: v3_orderableDrugForm_GasSolidSpray,
  /**
   * A state of substance that is an intermediate one entered into as matter goes from solid to gas; liquids are also intermediate in that they have neither the orderliness of a crystal nor the randomness of a gas. (Note: This term should not be used to describe solutions, only pure chemicals in their liquid state.)
   */
  Liquid: v3_orderableDrugForm_Liquid,
  /**
   * A two-phase system in which one liquid is dispersed throughout another liquid in the form of small droplets.
   */
  LiquidLiquidEmulsion: v3_orderableDrugForm_LiquidLiquidEmulsion,
  /**
   * A liquid preparation which consists of solid particles dispersed throughout a liquid phase in which the particles are not soluble.
   */
  LiquidSolidSuspension: v3_orderableDrugForm_LiquidSolidSuspension,
  /**
   * SolidDrugForm
   */
  SolidDrugForm: v3_orderableDrugForm_SolidDrugForm,
  /**
   * Aerosol
   */
  Aerosol: v3_orderableDrugForm_Aerosol,
  /**
   * Applicatorful
   */
  Applicatorful: v3_orderableDrugForm_Applicatorful,
  /**
   * Breath Activated Inhaler
   */
  BreathActivatedInhaler: v3_orderableDrugForm_BreathActivatedInhaler,
  /**
   * Breath Activated Powder Inhaler
   */
  BreathActivatedPowderInhaler: v3_orderableDrugForm_BreathActivatedPowderInhaler,
  /**
   * Bar
   */
  Bar: v3_orderableDrugForm_Bar,
  /**
   * Bar Soap
   */
  BarSoap: v3_orderableDrugForm_BarSoap,
  /**
   * A solid dosage form in the shape of a small ball.
   */
  Beads: v3_orderableDrugForm_Beads,
  /**
   * Buccal Tablet
   */
  BuccalTablet: v3_orderableDrugForm_BuccalTablet,
  /**
   * Cake
   */
  Cake: v3_orderableDrugForm_Cake,
  /**
   * A solid dosage form in which the drug is enclosed within either a hard or soft soluble container or "shell" made from a suitable form of gelatin.
   */
  Capsule: v3_orderableDrugForm_Capsule,
  /**
   * Caplet
   */
  Caplet: v3_orderableDrugForm_Caplet,
  /**
   * A substance that serves to produce solid union between two surfaces.
   */
  Cement: v3_orderableDrugForm_Cement,
  /**
   * A solid dosage form usually in the form of a rectangle that is meant to be chewed.
   */
  ChewableBar: v3_orderableDrugForm_ChewableBar,
  /**
   * A solid dosage form containing medicinal substances with or without suitable diluents that is intended to be chewed, producing a pleasant tasting residue in the oral cavity that is easily swallowed and does not leave a bitter or unpleasant after-taste.
   */
  ChewableTablet: v3_orderableDrugForm_ChewableTablet,
  /**
   * Coated Particles Tablet
   */
  CoatedParticlesTablet: v3_orderableDrugForm_CoatedParticlesTablet,
  /**
   * A semisolid dosage form containing one or more drug substances dissolved or dispersed in a suitable base; more recently, the term has been restricted to products consisting of oil-in-water emulsions or aqueous microcrystalline dispersions of long chain fatty acids or alcohols that are water washable and more cosmetically and aesthetically acceptable.
   */
  Cream: v3_orderableDrugForm_Cream,
  /**
   * A naturally produced angular solid of definite form in which the ultimate units from which it is built up are systematically arranged; they are usually evenly spaced on a regular space lattice.
   */
  Crystals: v3_orderableDrugForm_Crystals,
  /**
   * Dermal Spray
   */
  DermalSpray: v3_orderableDrugForm_DermalSpray,
  /**
   * A solid dosage form containing medicinal substances which disintegrates rapidly, usually within a matter of seconds, when placed upon the tongue.
   */
  DisintegratingTablet: v3_orderableDrugForm_DisintegratingTablet,
  /**
   * A circular plate-like organ or structure.
   */
  Disk: v3_orderableDrugForm_Disk,
  /**
   * A liquid preparation, intended for the irrigative cleansing of the vagina, that is prepared from powders, liquid solutions, or liquid concentrates and contains one or more chemical substances dissolved in a suitable solvent or mutually miscible solvents.
   */
  Douche: v3_orderableDrugForm_Douche,
  /**
   * Drops
   */
  Drops: v3_orderableDrugForm_Drops,
  /**
   * Delayed Release Tablet
   */
  DelayedReleaseTablet: v3_orderableDrugForm_DelayedReleaseTablet,
  /**
   * Enteric Coated Tablet
   */
  EntericCoatedTablet: v3_orderableDrugForm_EntericCoatedTablet,
  /**
   * A clear, pleasantly flavored, sweetened hydroalcoholic liquid containing dissolved medicinal agents; it is intended for oral use.
   */
  Elixir: v3_orderableDrugForm_Elixir,
  /**
   * A rectal preparation for therapeutic, diagnostic, or nutritive purposes.
   */
  Enema: v3_orderableDrugForm_Enema,
  /**
   * Enteric Coated Capsule
   */
  EntericCoatedCapsule: v3_orderableDrugForm_EntericCoatedCapsule,
  /**
   * A solid dosage form in which the drug is enclosed within either a hard or soft soluble container made from a suitable form of gelatin, and which releases a drug (or drugs) in such a manner to allow a reduction in dosing frequency as compared to that drug (or drugs) presented as a conventional dosage form.
   */
  ExtendedReleaseCapsule: v3_orderableDrugForm_ExtendedReleaseCapsule,
  /**
   * 12 Hour Extended Release Capsule
   */
  VAL12HourExtendedReleaseCapsule: v3_orderableDrugForm_VAL12HourExtendedReleaseCapsule,
  /**
   * 24 Hour Extended Release Capsule
   */
  VAL24HourExtendedReleaseCapsule: v3_orderableDrugForm_VAL24HourExtendedReleaseCapsule,
  /**
   * Rationale: Duplicate of code ERENTCAP. Use code ERENTCAP instead.
   */
  ExtendedReleaseEntericCoatedCapsule: v3_orderableDrugForm_ExtendedReleaseEntericCoatedCapsule,
  /**
   * Extended Release Enteric Coated Tablet
   */
  ExtendedReleaseEntericCoatedTablet: v3_orderableDrugForm_ExtendedReleaseEntericCoatedTablet,
  /**
   * Extended Release Enteric Coated Capsule
   */
  ExtendedReleaseEntericCoatedCapsule_2: v3_orderableDrugForm_ExtendedReleaseEntericCoatedCapsule_2,
  /**
   * Extended-Release Suspension
   */
  ExtendedReleaseSuspension: v3_orderableDrugForm_ExtendedReleaseSuspension,
  /**
   * 12 Hour Extended-Release Suspension
   */
  VAL12HourExtendedReleaseSuspension: v3_orderableDrugForm_VAL12HourExtendedReleaseSuspension,
  /**
   * 24 Hour Extended Release Suspension
   */
  VAL24HourExtendedReleaseSuspension: v3_orderableDrugForm_VAL24HourExtendedReleaseSuspension,
  /**
   * A solid dosage form containing a drug which allows at least a reduction in dosing frequency as compared to that drug presented in conventional dosage form.
   */
  ExtendedReleaseTablet: v3_orderableDrugForm_ExtendedReleaseTablet,
  /**
   * 12 Hour Extended Release Tablet
   */
  VAL12HourExtendedReleaseTablet: v3_orderableDrugForm_VAL12HourExtendedReleaseTablet,
  /**
   * 24 Hour Extended Release Tablet
   */
  VAL24HourExtendedReleaseTablet: v3_orderableDrugForm_VAL24HourExtendedReleaseTablet,
  /**
   * Flakes
   */
  Flakes: v3_orderableDrugForm_Flakes,
  /**
   * Foam
   */
  Foam: v3_orderableDrugForm_Foam,
  /**
   * Foam with Applicator
   */
  FoamWithApplicator: v3_orderableDrugForm_FoamWithApplicator,
  /**
   * Gas for Inhalation
   */
  GasForInhalation: v3_orderableDrugForm_GasForInhalation,
  /**
   * A semisolid system consisting of either suspensions made up of small inorganic particles or large organic molecules interpenetrated by a liquid.
   */
  Gel: v3_orderableDrugForm_Gel,
  /**
   * Gel with Applicator
   */
  GelWithApplicator: v3_orderableDrugForm_GelWithApplicator,
  /**
   * A small particle or grain.
   */
  Granules: v3_orderableDrugForm_Granules,
  /**
   * A sweetened and flavored insoluble plastic material of various shapes which when chewed, releases a drug substance into the oral cavity.
   */
  ChewingGum: v3_orderableDrugForm_ChewingGum,
  /**
   * Inhalant
   */
  Inhalant: v3_orderableDrugForm_Inhalant,
  /**
   * Inhalant Powder
   */
  InhalantPowder: v3_orderableDrugForm_InhalantPowder,
  /**
   * Inhalant Solution
   */
  InhalantSolution: v3_orderableDrugForm_InhalantSolution,
  /**
   * Intraperitoneal Solution
   */
  IntraperitonealSolution: v3_orderableDrugForm_IntraperitonealSolution,
  /**
   * A sterile solution intended to bathe or flush open wounds or body cavities; they're used topically, never parenterally.
   */
  IrrigationSolution: v3_orderableDrugForm_IrrigationSolution,
  /**
   * Intrathecal Suspension
   */
  IntrathecalSuspension: v3_orderableDrugForm_IntrathecalSuspension,
  /**
   * Intravenous Solution
   */
  IntravenousSolution: v3_orderableDrugForm_IntravenousSolution,
  /**
   * A solution or mixture of various substances in oil, alcoholic solutions of soap, or emulsions intended for external application.
   */
  Liniment: v3_orderableDrugForm_Liniment,
  /**
   * Liquid Cleanser
   */
  LiquidCleanser: v3_orderableDrugForm_LiquidCleanser,
  /**
   * Medicated Liquid Soap
   */
  MedicatedLiquidSoap: v3_orderableDrugForm_MedicatedLiquidSoap,
  /**
   * The term "lotion" has been used to categorize many topical suspensions, solutions and emulsions intended for application to the skin.
   */
  Lotion: v3_orderableDrugForm_Lotion,
  /**
   * Metered Dose Inhaler
   */
  MeteredDoseInhaler: v3_orderableDrugForm_MeteredDoseInhaler,
  /**
   * Metered Dose Powder Inhaler
   */
  MeteredDosePowderInhaler: v3_orderableDrugForm_MeteredDosePowderInhaler,
  /**
   * Medicated Bar Soap
   */
  MedicatedBarSoap: v3_orderableDrugForm_MedicatedBarSoap,
  /**
   * Medicated Pad
   */
  MedicatedPad: v3_orderableDrugForm_MedicatedPad,
  /**
   * Medicated swab
   */
  MedicatedSwab: v3_orderableDrugForm_MedicatedSwab,
  /**
   * Mucous Membrane Topical Solution
   */
  MucousMembraneTopicalSolution: v3_orderableDrugForm_MucousMembraneTopicalSolution,
  /**
   * Nasal Cream
   */
  NasalCream: v3_orderableDrugForm_NasalCream,
  /**
   * Nasal Gel
   */
  NasalGel: v3_orderableDrugForm_NasalGel,
  /**
   * Nasal Inhalant
   */
  NasalInhalant: v3_orderableDrugForm_NasalInhalant,
  /**
   * Nasal Ointment
   */
  NasalOintment: v3_orderableDrugForm_NasalOintment,
  /**
   * Nasal Spray
   */
  NasalSpray: v3_orderableDrugForm_NasalSpray,
  /**
   * Nasal Drops
   */
  NasalDrops: v3_orderableDrugForm_NasalDrops,
  /**
   * An unctuous, combustible substance which is liquid, or easily liquefiable, on warming, and is soluble in ether but insoluble in water. Such substances, depending on their origin, are classified as animal, mineral, or vegetable oils.
   */
  Oil: v3_orderableDrugForm_Oil,
  /**
   * A semisolid preparation intended for external application to the skin or mucous membranes.
   */
  Ointment: v3_orderableDrugForm_Ointment,
  /**
   * Ointment with Applicator
   */
  OintmentWithApplicator: v3_orderableDrugForm_OintmentWithApplicator,
  /**
   * Ophthalmic Cream
   */
  OphthalmicCream: v3_orderableDrugForm_OphthalmicCream,
  /**
   * Ophthalmic Drops
   */
  OphthalmicDrops: v3_orderableDrugForm_OphthalmicDrops,
  /**
   * Ophthalmic Gel
   */
  OphthalmicGel: v3_orderableDrugForm_OphthalmicGel,
  /**
   * Ophthalmic Irrigation Solution
   */
  OphthalmicIrrigationSolution: v3_orderableDrugForm_OphthalmicIrrigationSolution,
  /**
   * Ophthalmic Ointment
   */
  OphthalmicOintment: v3_orderableDrugForm_OphthalmicOintment,
  /**
   * Ophthalmic Suspension
   */
  OphthalmicSuspension: v3_orderableDrugForm_OphthalmicSuspension,
  /**
   * Oral Solution
   */
  OralSolution: v3_orderableDrugForm_OralSolution,
  /**
   * Oral Capsule
   */
  OralCapsule: v3_orderableDrugForm_OralCapsule,
  /**
   * Oral Cream
   */
  OralCream: v3_orderableDrugForm_OralCream,
  /**
   * Oral Drops
   */
  OralDrops: v3_orderableDrugForm_OralDrops,
  /**
   * Oral Inhalant
   */
  OralInhalant: v3_orderableDrugForm_OralInhalant,
  /**
   * Oral Suspension
   */
  OralSuspension: v3_orderableDrugForm_OralSuspension,
  /**
   * Oral Tablet
   */
  OralTablet: v3_orderableDrugForm_OralTablet,
  /**
   * A solid preparation containing one or more medicaments, usually in a flavored, sweetened base which is intended to dissolve or disintegrate slowly in the mouth.
   */
  LozengeOralTroche: v3_orderableDrugForm_LozengeOralTroche,
  /**
   * Otic Cream
   */
  OticCream: v3_orderableDrugForm_OticCream,
  /**
   * Otic Drops
   */
  OticDrops: v3_orderableDrugForm_OticDrops,
  /**
   * Otic Gel
   */
  OticGel: v3_orderableDrugForm_OticGel,
  /**
   * Otic Ointment
   */
  OticOintment: v3_orderableDrugForm_OticOintment,
  /**
   * Otic Suspension
   */
  OticSuspension: v3_orderableDrugForm_OticSuspension,
  /**
   * Pad
   */
  Pad: v3_orderableDrugForm_Pad,
  /**
   * A semisolid dosage form that contains one or more drug substances intended for topical application.
   */
  Paste: v3_orderableDrugForm_Paste,
  /**
   * A drug delivery system that contains an adhesived backing and that permits its ingredients to diffuse from some portion of it (e.g., the backing itself, a reservoir, the adhesive, or some other component) into the body from the external site where it is applied.
   */
  Patch: v3_orderableDrugForm_Patch,
  /**
   * A small sterile solid mass consisting of a highly purified drug (with or without excipients) made by the formation of granules, or by compression and molding.
   */
  Pellet: v3_orderableDrugForm_Pellet,
  /**
   * A small, round solid dosage form containing a medicinal agent intended for oral administration.
   */
  Pill: v3_orderableDrugForm_Pill,
  /**
   * An intimate mixture of dry, finely divided drugs and/or chemicals that may be intended for internal or external use.
   */
  Powder: v3_orderableDrugForm_Powder,
  /**
   * Pudding
   */
  Pudding: v3_orderableDrugForm_Pudding,
  /**
   * Puff
   */
  Puff: v3_orderableDrugForm_Puff,
  /**
   * Powder Spray
   */
  PowderSpray: v3_orderableDrugForm_PowderSpray,
  /**
   * Rectal Cream
   */
  RectalCream: v3_orderableDrugForm_RectalCream,
  /**
   * Rectal foam
   */
  RectalFoam: v3_orderableDrugForm_RectalFoam,
  /**
   * Rectal Ointment
   */
  RectalOintment: v3_orderableDrugForm_RectalOintment,
  /**
   * Rectal Powder
   */
  RectalPowder: v3_orderableDrugForm_RectalPowder,
  /**
   * Rectal Solution
   */
  RectalSolution: v3_orderableDrugForm_RectalSolution,
  /**
   * Rectal Spray
   */
  RectalSpray: v3_orderableDrugForm_RectalSpray,
  /**
   * Rectal Suppository
   */
  RectalSuppository: v3_orderableDrugForm_RectalSuppository,
  /**
   * Rectal Suspension
   */
  RectalSuspension: v3_orderableDrugForm_RectalSuspension,
  /**
   * An aqueous solution which is most often used for its deodorant, refreshing, or antiseptic effect.
   */
  MouthwashRinse: v3_orderableDrugForm_MouthwashRinse,
  /**
   * Scoops
   */
  Scoops: v3_orderableDrugForm_Scoops,
  /**
   * A liquid soap or detergent used to clean the hair and scalp and is often used as a vehicle for dermatologic agents.
   */
  Shampoo: v3_orderableDrugForm_Shampoo,
  /**
   * Sublingual Tablet
   */
  SublingualTablet: v3_orderableDrugForm_SublingualTablet,
  /**
   * A liquid preparation that contains one or more chemical substances dissolved, i.e., molecularly dispersed, in a suitable solvent or mixture of mutually miscible solvents.
   */
  Solution: v3_orderableDrugForm_Solution,
  /**
   * Sprays
   */
  Sprays: v3_orderableDrugForm_Sprays,
  /**
   * Spray with Adaptor
   */
  SprayWithAdaptor: v3_orderableDrugForm_SprayWithAdaptor,
  /**
   * Sustained Release Buccal Tablet
   */
  SustainedReleaseBuccalTablet: v3_orderableDrugForm_SustainedReleaseBuccalTablet,
  /**
   * A solid body of various weights and shapes, adapted for introduction into the rectal, vaginal, or urethral orifice of the human body; they usually melt, soften, or dissolve at body temperature.
   */
  Suppository: v3_orderableDrugForm_Suppository,
  /**
   * Suspension
   */
  Suspension: v3_orderableDrugForm_Suspension,
  /**
   * A wad of absorbent material usually wound around one end of a small stick and used for applying medication or for removing material from an area.
   */
  Swab: v3_orderableDrugForm_Swab,
  /**
   * An oral solution containing high concentrations of sucrose or other sugars; the term has also been used to include any other liquid dosage form prepared in a sweet and viscid vehicle, including oral suspensions.
   */
  Syrup: v3_orderableDrugForm_Syrup,
  /**
   * A solid dosage form containing medicinal substances with or without suitable diluents.
   */
  Tablet: v3_orderableDrugForm_Tablet,
  /**
   * Tincture
   */
  Tincture: v3_orderableDrugForm_Tincture,
  /**
   * Topical Cream
   */
  TopicalCream: v3_orderableDrugForm_TopicalCream,
  /**
   * Topical Gel
   */
  TopicalGel: v3_orderableDrugForm_TopicalGel,
  /**
   * Topical Lotion
   */
  TopicalLotion: v3_orderableDrugForm_TopicalLotion,
  /**
   * Topical Oil
   */
  TopicalOil: v3_orderableDrugForm_TopicalOil,
  /**
   * Topical Ointment
   */
  TopicalOintment: v3_orderableDrugForm_TopicalOintment,
  /**
   * Topical Powder
   */
  TopicalPowder: v3_orderableDrugForm_TopicalPowder,
  /**
   * Topical Solution
   */
  TopicalSolution: v3_orderableDrugForm_TopicalSolution,
  /**
   * A paste formulation intended to clean and/or polish the teeth, and which may contain certain additional agents.
   */
  Toothpaste: v3_orderableDrugForm_Toothpaste,
  /**
   * Transdermal Patch
   */
  TransdermalPatch: v3_orderableDrugForm_TransdermalPatch,
  /**
   * 16 Hour Transdermal Patch
   */
  VAL16HourTransdermalPatch: v3_orderableDrugForm_VAL16HourTransdermalPatch,
  /**
   * 24 Hour Transdermal Patch
   */
  VAL24HourTransdermalPatch: v3_orderableDrugForm_VAL24HourTransdermalPatch,
  /**
   * Biweekly Transdermal Patch
   */
  BiweeklyTransdermalPatch: v3_orderableDrugForm_BiweeklyTransdermalPatch,
  /**
   * 72 Hour Transdermal Patch
   */
  VAL72HourTransdermalPatch: v3_orderableDrugForm_VAL72HourTransdermalPatch,
  /**
   * Weekly Transdermal Patch
   */
  WeeklyTransdermalPatch: v3_orderableDrugForm_WeeklyTransdermalPatch,
  /**
   * Urethral Gel
   */
  UrethralGel: v3_orderableDrugForm_UrethralGel,
  /**
   * Urethral suppository
   */
  UrethralSuppository: v3_orderableDrugForm_UrethralSuppository,
  /**
   * Vaginal Cream
   */
  VaginalCream: v3_orderableDrugForm_VaginalCream,
  /**
   * Vaginal Cream with Applicator
   */
  VaginalCreamWithApplicator: v3_orderableDrugForm_VaginalCreamWithApplicator,
  /**
   * Vaginal foam
   */
  VaginalFoam: v3_orderableDrugForm_VaginalFoam,
  /**
   * Vaginal foam with applicator
   */
  VaginalFoamWithApplicator: v3_orderableDrugForm_VaginalFoamWithApplicator,
  /**
   * Vaginal Gel
   */
  VaginalGel: v3_orderableDrugForm_VaginalGel,
  /**
   * Vaginal Ointment
   */
  VaginalOintment: v3_orderableDrugForm_VaginalOintment,
  /**
   * Vaginal Ointment with Applicator
   */
  VaginalOintmentWithApplicator: v3_orderableDrugForm_VaginalOintmentWithApplicator,
  /**
   * Vaginal Powder
   */
  VaginalPowder: v3_orderableDrugForm_VaginalPowder,
  /**
   * Vaginal Spray
   */
  VaginalSpray: v3_orderableDrugForm_VaginalSpray,
  /**
   * Vaginal Suppository
   */
  VaginalSuppository: v3_orderableDrugForm_VaginalSuppository,
  /**
   * Vaginal Tablet
   */
  VaginalTablet: v3_orderableDrugForm_VaginalTablet,
  /**
   * Vaginal Gel with Applicator
   */
  VaginalGelWithApplicator: v3_orderableDrugForm_VaginalGelWithApplicator,
  /**
   * A thin slice of material containing a medicinal agent.
   */
  Wafer: v3_orderableDrugForm_Wafer,
};
const v3_ParticipationFunction_AuthorizedParticipationFunction: Coding = {
  code: '_AuthorizedParticipationFunction',
  display: 'AuthorizedParticipationFunction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_AuthorizedReceiverParticipationFunction: Coding = {
  code: '_AuthorizedReceiverParticipationFunction',
  display: 'AuthorizedReceiverParticipationFunction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_ConsenterParticipationFunction: Coding = {
  code: '_ConsenterParticipationFunction',
  display: 'ConsenterParticipationFunction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_CoverageParticipationFunction: Coding = {
  code: '_CoverageParticipationFunction',
  display: 'CoverageParticipationFunction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_OverriderParticipationFunction: Coding = {
  code: '_OverriderParticipationFunction',
  display: 'OverriderParticipationFunction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_PayorParticipationFunction: Coding = {
  code: '_PayorParticipationFunction',
  display: 'PayorParticipationFunction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_SponsorParticipationFunction: Coding = {
  code: '_SponsorParticipationFunction',
  display: 'SponsorParticipationFunction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_UnderwriterParticipationFunction: Coding = {
  code: '_UnderwriterParticipationFunction',
  display: 'UnderwriterParticipationFunction',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_AdmittingPhysician: Coding = {
  code: 'ADMPHYS',
  display: 'admitting physician',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_Anesthesist: Coding = {
  code: 'ANEST',
  display: 'anesthesist',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_AnesthesiaNurse: Coding = {
  code: 'ANRS',
  display: 'anesthesia nurse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_AssemblySoftware: Coding = {
  code: 'ASSEMBLER',
  display: 'assembly software',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_AttendingPhysician: Coding = {
  code: 'ATTPHYS',
  display: 'attending physician',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_ConsentOverrider: Coding = {
  code: 'AUCOV',
  display: 'consent overrider',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_EmergencyOverrider: Coding = {
  code: 'AUEMROV',
  display: 'emergency overrider',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_ClaimsAdjudication: Coding = {
  code: 'CLMADJ',
  display: 'claims adjudication',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_ComposerSoftware: Coding = {
  code: 'COMPOSER',
  display: 'composer software',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_DischargingPhysician: Coding = {
  code: 'DISPHYS',
  display: 'discharging physician',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_EnrollmentBroker: Coding = {
  code: 'ENROLL',
  display: 'enrollment broker',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_FirstAssistantSurgeon: Coding = {
  code: 'FASST',
  display: 'first assistant surgeon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_FfsManagement: Coding = {
  code: 'FFSMGT',
  display: 'ffs management',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_FullyInsured: Coding = {
  code: 'FULINRD',
  display: 'fully insured',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_LegalGuardianConsentAuthor: Coding = {
  code: 'GRDCON',
  display: 'legal guardian consent author',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_ManagedCareManagement: Coding = {
  code: 'MCMGT',
  display: 'managed care management',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_Midwife: Coding = {
  code: 'MDWF',
  display: 'midwife',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_NurseAssistant: Coding = {
  code: 'NASST',
  display: 'nurse assistant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_PayorContracting: Coding = {
  code: 'PAYORCNTR',
  display: 'payor contracting',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_PrimaryCarePhysician: Coding = {
  code: 'PCP',
  display: 'primary care physician',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_HealthcarePowerOfAttorneyConsentAuthor: Coding = {
  code: 'POACON',
  display: 'healthcare power of attorney consent author',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_PersonalRepresentativeConsentAuthor: Coding = {
  code: 'PRCON',
  display: 'personal representative consent author',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_PrimarySurgeon: Coding = {
  code: 'PRISURG',
  display: 'primary surgeon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_ProviderManagement: Coding = {
  code: 'PROVMGT',
  display: 'provider management',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_Reinsures: Coding = {
  code: 'REINS',
  display: 'reinsures',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_Retrocessionaires: Coding = {
  code: 'RETROCES',
  display: 'retrocessionaires',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_Reviewer: Coding = {
  code: 'REVIEWER',
  display: 'reviewer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_RoundingPhysician: Coding = {
  code: 'RNDPHYS',
  display: 'rounding physician',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_SecondAssistantSurgeon: Coding = {
  code: 'SASST',
  display: 'second assistant surgeon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_SelfInsured: Coding = {
  code: 'SELFINRD',
  display: 'self insured',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_ScrubNurse: Coding = {
  code: 'SNRS',
  display: 'scrub nurse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_SubjectOfConsentAuthor: Coding = {
  code: 'SUBCON',
  display: 'subject of consent author',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_SubcontractingRisk: Coding = {
  code: 'SUBCTRT',
  display: 'subcontracting risk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_ThirdAssistant: Coding = {
  code: 'TASST',
  display: 'third assistant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_UtilizationManagement: Coding = {
  code: 'UMGT',
  display: 'utilization management',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
const v3_ParticipationFunction_Underwriting: Coding = {
  code: 'UNDERWRTNG',
  display: 'underwriting',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationFunction',
};
/**
 *  This code is used to specify the exact function an actor had in a service in all necessary detail. This domain may include local extensions (CWE).
 */
export const V3ParticipationFunction = {
  /**
   * This code is used to specify the exact function an actor is authorized to have in a service in all necessary detail.
   */
  AuthorizedParticipationFunction: v3_ParticipationFunction_AuthorizedParticipationFunction,
  /**
   * This code is used to specify the exact function an actor is authorized to have as a receiver of information that is the subject of a consent directive or consent override.
   */
  AuthorizedReceiverParticipationFunction: v3_ParticipationFunction_AuthorizedReceiverParticipationFunction,
  /**
   * This code is used to specify the exact function an actor is authorized to have in authoring a consent directive.
   */
  ConsenterParticipationFunction: v3_ParticipationFunction_ConsenterParticipationFunction,
  /**
   * Definition: Set of codes indicating the manner in which sponsors, underwriters, and payers participate in a policy or program.
   */
  CoverageParticipationFunction: v3_ParticipationFunction_CoverageParticipationFunction,
  /**
   * This code is used to specify the exact function an actor is authorized to have in authoring a consent override.
   */
  OverriderParticipationFunction: v3_ParticipationFunction_OverriderParticipationFunction,
  /**
   * Definition: Set of codes indicating the manner in which payors participate in a policy or program.</
   */
  PayorParticipationFunction: v3_ParticipationFunction_PayorParticipationFunction,
  /**
   * Definition: Set of codes indicating the manner in which sponsors participate in a policy or program. NOTE: use only when the Sponsor is not further specified with a SponsorRoleType as being either a fully insured sponsor or a self insured sponsor.
   */
  SponsorParticipationFunction: v3_ParticipationFunction_SponsorParticipationFunction,
  /**
   * Definition: Set of codes indicating the manner in which underwriters participate in a policy or program.
   */
  UnderwriterParticipationFunction: v3_ParticipationFunction_UnderwriterParticipationFunction,
  /**
   * A physician who admitted a patient to a hospital or other care unit that is the context of this service.
   */
  AdmittingPhysician: v3_ParticipationFunction_AdmittingPhysician,
  /**
   * In a typical anesthesia setting an anesthesiologist or anesthesia resident in charge of the anesthesia and life support, but only a witness to the surgical procedure itself.  To clarify responsibilities anesthesia should always be represented as a separate service related to the surgery.
   */
  Anesthesist: v3_ParticipationFunction_Anesthesist,
  /**
   * In a typical anesthesia setting the nurse principally assisting the anesthesiologist during the critical periods.
   */
  AnesthesiaNurse: v3_ParticipationFunction_AnesthesiaNurse,
  /**
   * A device that operates independently of an author on custodian's algorithms for data extraction of existing information for purpose of generating a new artifact.
   *                            UsageConstraint: ASSEMBLER ParticipationFunction should be used with DEV (device) ParticipationType.
   */
  AssemblySoftware: v3_ParticipationFunction_AssemblySoftware,
  /**
   * A physician who is primarily responsible for a patient during the hospitalization, which is the context of the service.
   */
  AttendingPhysician: v3_ParticipationFunction_AttendingPhysician,
  /**
   * Description:Caregiver authorized to receive patient health information.
   */
  CaregiverInformationReceiver: v3_ParticipationFunction_CaregiverInformationReceiver,
  /**
   * Description:Entity authorized to override a consent directive.
   */
  ConsentOverrider: v3_ParticipationFunction_ConsentOverrider,
  /**
   * Description:Entity  authorized to override a consent directive or privacy policy in an emergency.
   */
  EmergencyOverrider: v3_ParticipationFunction_EmergencyOverrider,
  /**
   * Description:Provider with legitimate relationship authorized to receive patient health information.
   */
  LegitimateRelationshipInformationReceiver: v3_ParticipationFunction_LegitimateRelationshipInformationReceiver,
  /**
   * Description:Member of care team authorized to receive patient health information.
   */
  CareTeamInformationReceiver: v3_ParticipationFunction_CareTeamInformationReceiver,
  /**
   * Description:Entities within specified work area authorized to receive patient health information.
   */
  WorkAreaInformationReceiver: v3_ParticipationFunction_WorkAreaInformationReceiver,
  /**
   * Definition: Manages all operations required to adjudicate fee for service claims or managed care encounter reports.
   */
  ClaimsAdjudication: v3_ParticipationFunction_ClaimsAdjudication,
  /**
   * A device used by an author to record new information, which may also be used by the author to select existing information for aggregation with newly recorded information for the purpose of generating a new artifact.
   *                            UsageConstraint: COMPOSER ParticipationFunction should be used with DEV (device) ParticipationType.
   *
   *
   *                            Usage Note: This code will enable implementers to more specifically represent the manner in which a Device participated in and facilitated the generation of a CDA Clinical Document or a CDA Entry by the responsible Author, which is comprised of the Author's newly entered content, and may include the pre-existing content selected by the Author, for the purpose of establishing the provenance and accountability for these acts.
   */
  ComposerSoftware: v3_ParticipationFunction_ComposerSoftware,
  /**
   * A physician who discharged a patient from a hospital or other care unit that is the context of this service.
   */
  DischargingPhysician: v3_ParticipationFunction_DischargingPhysician,
  /**
   * Definition: Managing the enrollment of covered parties.
   */
  EnrollmentBroker: v3_ParticipationFunction_EnrollmentBroker,
  /**
   * In a typical surgery setting the assistant facing the primary surgeon.  The first assistant performs parts of the operation and assists in others (e.g., incision, approach, electrocoutering, ligatures, sutures).
   */
  FirstAssistantSurgeon: v3_ParticipationFunction_FirstAssistantSurgeon,
  /**
   * Definition: Managing all operations required to administer a fee for service or indemnity health plan including enrolling covered parties and providing customer service, provider contracting, claims payment, care management and utilization review.
   */
  FfsManagement: v3_ParticipationFunction_FfsManagement,
  /**
   * Definition: Responsibility taken by a sponsor to contract with one or more underwriters for the assumption of full responsibility for the risk and administration of a policy or program.
   */
  FullyInsured: v3_ParticipationFunction_FullyInsured,
  /**
   * Description:Legal guardian of the subject of consent authorized to author a consent directive for the subject of consent.
   */
  LegalGuardianConsentAuthor: v3_ParticipationFunction_LegalGuardianConsentAuthor,
  /**
   * Definition: Managing all operations required to administer a managed care plan including enrolling covered parties and providing customer service,, provider contracting, claims payment, care management and utilization review.
   */
  ManagedCareManagement: v3_ParticipationFunction_ManagedCareManagement,
  /**
   * A person (usually female) helping a woman deliver a baby. Responsibilities vary locally, ranging from a mere optional assistant to a full required participant, responsible for (normal) births and pre- and post-natal care for both mother and baby.
   */
  Midwife: v3_ParticipationFunction_Midwife,
  /**
   * In a typical surgery setting the non-sterile nurse handles material supply from the stock, forwards specimen to pathology, and helps with other non-sterile tasks (e.g., phone calls, etc.).
   */
  NurseAssistant: v3_ParticipationFunction_NurseAssistant,
  /**
   * Definition: Contracting for the provision and administration of health services to payors while retaining the risk for coverage.  Contracting may be for all provision and administration; or for provision of certain types of services; for provision of services by region; and by types of administration, e.g., claims adjudication, enrollment, provider management, and utilization management.  Typically done by underwriters for sponsors who need coverage provided to covered parties in multiple regions.  The underwriter may act as the payor in some, but not all of the regions in which coverage is provided.
   */
  PayorContracting: v3_ParticipationFunction_PayorContracting,
  /**
   * The healthcare provider that holds primary responsibility for the overall care of a patient.
   */
  PrimaryCarePhysician: v3_ParticipationFunction_PrimaryCarePhysician,
  /**
   * Description:Person authorized with healthcare power of attorney to author a  consent directive for the subject of consent.
   */
  HealthcarePowerOfAttorneyConsentAuthor: v3_ParticipationFunction_HealthcarePowerOfAttorneyConsentAuthor,
  /**
   * Description:Personal representative of the subject of consent authorized to author a consent directive for the subject of consent.
   */
  PersonalRepresentativeConsentAuthor: v3_ParticipationFunction_PersonalRepresentativeConsentAuthor,
  /**
   * In a typical surgery setting the primary performing surgeon.
   */
  PrimarySurgeon: v3_ParticipationFunction_PrimarySurgeon,
  /**
   * Definition:Provider authorized to mask information to protect the patient, a third party, or to ensure that the provider has consulted with the patient prior to release of this information.
   */
  AuthorizedProviderMaskingAuthor: v3_ParticipationFunction_AuthorizedProviderMaskingAuthor,
  /**
   * Definition: Managing provider contracting, provider services, credentialing, profiling, performance measures, and ensuring network adequacy.
   */
  ProviderManagement: v3_ParticipationFunction_ProviderManagement,
  /**
   * Definition: Underwriting reinsurance for another underwriter for the policy or program.
   */
  Reinsures: v3_ParticipationFunction_Reinsures,
  /**
   * Definition: Underwriting reinsurance for another reinsurer.
   */
  Retrocessionaires: v3_ParticipationFunction_Retrocessionaires,
  /**
   * A verifier who is accountable for reviewing and asserting that the verification of an Act complies with jurisdictional or organizational policy.
   *
   *
   *                            UsageConstraint: UsageConstraint:  Specifies the exact function that an actor is authorized to have as a verifier of an Act.  Connotes that a specialized verifier asserts compliance for veracity of the review per jurisdictional or organizational policy.  E.g., The Provider who takes responsibility for authenticity of a record submitted to a payer.
   *
   *                         REVIEW ParticipationFunction should be used with VFR (verifier)
   */
  Reviewer: v3_ParticipationFunction_Reviewer,
  /**
   * A physician who made rounds on a patient in a hospital or other care center.
   */
  RoundingPhysician: v3_ParticipationFunction_RoundingPhysician,
  /**
   * In a typical surgery setting the assistant who primarily holds the hooks.
   */
  SecondAssistantSurgeon: v3_ParticipationFunction_SecondAssistantSurgeon,
  /**
   * Definition: Responsibility taken by a sponsor to organize the underwriting of risk and administration of a policy or program.
   */
  SelfInsured: v3_ParticipationFunction_SelfInsured,
  /**
   * In a typical surgery setting the nurse in charge of the instrumentation.
   */
  ScrubNurse: v3_ParticipationFunction_ScrubNurse,
  /**
   * Description:Subject of consent authorized to author a consent directive.
   */
  SubjectOfConsentAuthor: v3_ParticipationFunction_SubjectOfConsentAuthor,
  /**
   * Definition: Delegating risk for a policy or program to one or more subcontracting underwriters, e.g., a major health insurer may delegate risk for provision of coverage under a national health plan to other underwriters by region .
   */
  SubcontractingRisk: v3_ParticipationFunction_SubcontractingRisk,
  /**
   * In a typical surgery setting there is rarely a third assistant (e.g., in some Hip operations the third assistant postures the affected leg).
   */
  ThirdAssistant: v3_ParticipationFunction_ThirdAssistant,
  /**
   * Definition: Managing utilization of services by ensuring that providers adhere to, e.g., payeraTMs clinical protocols for medical appropriateness and standards of medical necessity.  May include management of authorizations for services and referrals.
   */
  UtilizationManagement: v3_ParticipationFunction_UtilizationManagement,
  /**
   * Definition: Provision of underwriting analysis for another underwriter without assumption of risk.
   */
  Underwriting: v3_ParticipationFunction_Underwriting,
};
/**
 *  Target that is not substantially present in the act and which is not directly affected by the act, but which will be a focus of the record or documentation of the act.
 */
export const V3ParticipationIndirectTarget = {
  /**
   * Target on behalf of whom the service happens, but that is not necessarily present in the service.  Can occur together with direct target to indicate that a target is both, as in the case where the patient is the indirect beneficiary of a service rendered to a family member, e.g. counseling or given home care instructions.  This concept includes a participant, such as a covered party, who derives benefits from a service act covered by a coverage act.
   *
   *                         Note that the semantic role of the intended recipient who benefits from the happening denoted by the verb in the clause.  Thus, a patient who has no coverage under a policy or program may be a beneficiary of a health service while not being the beneficiary of coverage for that service.
   */
  Beneficiary: v3_ParticipationType_Beneficiary,
  /**
   * Definition: A factor, such as a microorganism, chemical substance, or form of radiation, whose presence, excessive presence, or (in deficiency diseases) relative absence is essential, in whole or in part, for the occurrence of a condition.
   *
   *                         Constraint:  The use of this participation is limited to observations.
   */
  CausativeAgent: v3_ParticipationType_CausativeAgent,
  /**
   * The target participation for an individual in a health care coverage act in which the target role is either the policy holder of the coverage, or a covered party under the coverage.
   */
  CoverageTarget: v3_ParticipationType_CoverageTarget,
  /**
   * The target person or organization contractually recognized by the issuer as a participant who has assumed fiscal responsibility for another personaTMs financial obligations by guaranteeing to pay for amounts owed to a particular account
   *
   *
   *                            Example:The subscriber of the patientaTMs health insurance policy signs a contract with the provider to be fiscally responsible for the patient billing account balance amount owed.
   */
  GuarantorParty: v3_ParticipationType_GuarantorParty,
  /**
   * Participant who posses an instrument such as a financial contract (insurance policy) usually based on some agreement with the author.
   */
  Holder: v3_ParticipationType_Holder,
  /**
   * Target that is not substantially present in the act and which is not directly affected by the act, but which will be a focus of the record or documentation of the act.
   */
  IndirectTarget: v3_ParticipationType_IndirectTarget,
  /**
   * The record target indicates whose medical record holds the documentation of this act.  This is especially important when the subject of a service is not the patient himself.
   */
  RecordTarget: v3_ParticipationType_RecordTarget,
  /**
   * The person (or organization) who receives the product of an Act.
   */
  Receiver: v3_ParticipationType_Receiver,
};
/**
 *  Parties that may or should contribute or have contributed information to the Act. Such information includes information leading to the decision to perform the Act and how to perform the Act (e.g., consultant), information that the Act itself seeks to reveal (e.g., informant of clinical history), or information about what Act was performed (e.g., informant witness).
 */
export const V3ParticipationInformationGenerator = {
  /**
   * Definition: A party that originates the Act and therefore has responsibility for the information given in the Act and ownership of this Act.
   *
   *
   *                            Example: the report writer, the person writing the act definition, the guideline author, the placer of an order, the EKG cart (device) creating a report etc. Every Act should have an author. Authorship is regardless of mood always actual authorship.
   *
   *                         Examples of such policies might include:
   *
   *
   *
   *                               The author and anyone they explicitly delegate may update the report;
   *
   *
   *
   *                               All administrators within the same clinic may cancel and reschedule appointments created by other administrators within that clinic;
   *
   *
   *
   *                         A party that is neither an author nor a party who is extended authorship maintenance rights by policy, may only amend, reverse, override, replace, or follow up in other ways on this Act, whereby the Act remains intact and is linked to another Act authored by that other party.
   */
  AuthorOriginator: v3_ParticipationType_AuthorOriginator,
  /**
   * A person entering the data into the originating system.  The data entry person is collected optionally for internal quality control purposes.  This includes the transcriptionist for dictated text.
   */
  DataEntryPerson: v3_ParticipationType_DataEntryPerson,
  /**
   * A source of reported information (e.g., a next of kin who answers questions about the patient's history).  For history questions, the patient is logically an informant, yet the informant of history questions is implicitly the subject.
   */
  Informant: v3_ParticipationType_Informant,
  /**
   * An entity entering the data into the originating system. The data entry entity is collected optionally for internal quality control purposes. This includes the transcriptionist for dictated text transcribed into electronic form.
   */
  Transcriber: v3_ParticipationType_Transcriber,
  /**
   * Only with service events.  A person witnessing the action happening without doing anything.  A witness is not necessarily aware, much less approves of anything stated in the service event.  Example for a witness is students watching an operation or an advanced directive witness.
   */
  Witness: v3_ParticipationType_Witness,
};
/**
 *  An entity entering the data into the originating system. The data entry entity is collected optionally for internal quality control purposes. This includes the transcriptionist for dictated text transcribed into electronic form.
 */
export const V3ParticipationInformationTranscriber = {
  /**
   * A person entering the data into the originating system.  The data entry person is collected optionally for internal quality control purposes.  This includes the transcriptionist for dictated text.
   */
  DataEntryPerson: v3_ParticipationType_DataEntryPerson,
  /**
   * An entity entering the data into the originating system. The data entry entity is collected optionally for internal quality control purposes. This includes the transcriptionist for dictated text transcribed into electronic form.
   */
  Transcriber: v3_ParticipationType_Transcriber,
};
const v3_ParticipationMode_Dictated: Coding = {
  code: 'DICTATE',
  display: 'dictated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
const v3_ParticipationMode_Email: Coding = {
  code: 'EMAILWRIT',
  display: 'email',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
const v3_ParticipationMode_FaceToFace: Coding = {
  code: 'FACE',
  display: 'face-to-face',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
const v3_ParticipationMode_Telefax: Coding = {
  code: 'FAXWRIT',
  display: 'telefax',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
const v3_ParticipationMode_Handwritten: Coding = {
  code: 'HANDWRIT',
  display: 'handwritten',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
const v3_ParticipationMode_Mail: Coding = {
  code: 'MAILWRIT',
  display: 'mail',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
const v3_ParticipationMode_OnlineWritten: Coding = {
  code: 'ONLINEWRIT',
  display: 'online written',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
const v3_ParticipationMode_Telephone: Coding = {
  code: 'PHONE',
  display: 'telephone',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
const v3_ParticipationMode_PhysicalPresence: Coding = {
  code: 'PHYSICAL',
  display: 'physical presence',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
const v3_ParticipationMode_RemotePresence: Coding = {
  code: 'REMOTE',
  display: 'remote presence',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
const v3_ParticipationMode_Typewritten: Coding = {
  code: 'TYPEWRIT',
  display: 'typewritten',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
const v3_ParticipationMode_Videoconferencing: Coding = {
  code: 'VIDEOCONF',
  display: 'videoconferencing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
};
/**
 *  A set of codes specifying the modality by which the Entity playing the Role is participating in the Act.  Examples:
 * Physically present, over the telephone, written communication.  Rationale:
 * Particularly for author (originator) participants this is used to specify whether the information represented by the act was initially provided verbally, (hand-)written, or electronically.  Open Issue:
 * There needs to be a reexamination of the hierarchies as there seems to be some muddling between ELECTRONIC and other concepts that involve electronic communication that are in other hierarchies.
 */
export const V3ParticipationMode = {
  /**
   * Participation by pre-recorded voice.  Communication is limited to one direction (from the recorder to recipient).
   */
  Dictated: v3_ParticipationMode_Dictated,
  /**
   * Participation by non-human-languaged based electronic signal
   */
  ElectronicData: v3_ParticipationMode_ElectronicData,
  /**
   * Participation by text or diagrams transmitted over an electronic mail system.
   */
  Email: v3_ParticipationMode_Email,
  /**
   * Participation by voice communication where parties speak to each other directly.
   */
  FaceToFace: v3_ParticipationMode_FaceToFace,
  /**
   * Participation by text or diagrams printed on paper that have been transmitted over a fax device
   */
  Telefax: v3_ParticipationMode_Telefax,
  /**
   * Participation by text or diagrams printed on paper or other recording medium
   */
  Handwritten: v3_ParticipationMode_Handwritten,
  /**
   * Participation by text or diagrams printed on paper transmitted physically (e.g. by courier service, postal service).
   */
  Mail: v3_ParticipationMode_Mail,
  /**
   * Participation by text or diagrams submitted by computer network, e.g. online survey.
   */
  OnlineWritten: v3_ParticipationMode_OnlineWritten,
  /**
   * Participation by voice communication where the voices of the communicating parties are transported over an electronic medium
   */
  Telephone: v3_ParticipationMode_Telephone,
  /**
   * Participation by direct action where subject and actor are in the same location. (The participation involves more than communication.)
   */
  PhysicalPresence: v3_ParticipationMode_PhysicalPresence,
  /**
   * Participation by direct action where subject and actor are in separate locations, and the actions of the actor are transmitted by electronic or mechanical means. (The participation involves more than communication.)
   */
  RemotePresence: v3_ParticipationMode_RemotePresence,
  /**
   * Participation by text or diagrams printed on paper or other recording medium where the recording was performed using a typewriter, typesetter, computer or similar mechanism.
   */
  Typewritten: v3_ParticipationMode_Typewritten,
  /**
   * Participation by voice communication
   */
  Verbal: v3_ParticipationMode_Verbal,
  /**
   * Participation by voice and visual communication where the voices and images of the communicating parties are transported over an electronic medium
   */
  Videoconferencing: v3_ParticipationMode_Videoconferencing,
  /**
   * Participation by human language recorded on a physical material
   */
  Written: v3_ParticipationMode_Written,
};
/**
 *  A person  who actually and principally carries out the action.  Need not be the principal responsible actor, e.g. a surgery resident operating under supervision of attending surgeon, and may be the patient in self-care, e.g. fingerstick blood sugar.  The traditional order filler is a performer. This information should accompany every service event.
 */
export const V3ParticipationPhysicalPerformer = {
  /**
   * Distributes material used in or generated during the act.
   */
  Distributor: v3_ParticipationType_Distributor,
  /**
   * The principal or primary performer of the act.
   */
  PrimaryPerformer: v3_ParticipationType_PrimaryPerformer,
  /**
   * Definition: A person, non-person living subject, organization or device that who actually and principally carries out the action. Device should only be assigned as a performer in circumstances where the device is performing independent of human intervention.  Need not be the principal responsible actor.
   *
   *
   *                            Exampe: A surgery resident operating under supervision of attending surgeon, a search and rescue dog locating survivors, an electronic laboratory analyzer or the laboratory discipline requested to perform a laboratory test. The performer may also be the patient in self-care, e.g. fingerstick blood sugar. The traditional order filler is a performer. This information should accompany every service event.
   *
   *
   *                            Note: that existing HL7 designs assign an organization as the playing entity of the Role that is the performer.  These designs should be revised in subsequent releases to make this the scooping entity for the role involved.
   */
  Performer: v3_ParticipationType_Performer,
  /**
   * A person assisting in an act through his substantial presence and involvement   This includes: assistants, technicians, associates, or whatever the job titles may be.
   */
  SecondaryPerformer: v3_ParticipationType_SecondaryPerformer,
};
const v3_ParticipationSignature_Intended: Coding = {
  code: 'I',
  display: 'intended',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationSignature',
};
const v3_ParticipationSignature_Signed: Coding = {
  code: 'S',
  display: 'signed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationSignature',
};
const v3_ParticipationSignature_Required: Coding = {
  code: 'X',
  display: 'required',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationSignature',
};
/**
 *  A set of codes specifying whether and how the participant has attested his participation through a signature and or whether such a signature is needed.  Examples:
 * A surgical Procedure act object (representing a procedure report) requires a signature of the performing and responsible surgeon, and possibly other participants. (See also: Participation.signatureText.)
 */
export const V3ParticipationSignature = {
  /**
   * The particpant intends to provide a signature.
   */
  Intended: v3_ParticipationSignature_Intended,
  /**
   * Signature has been affixed, either written on file, or electronic (incl. digital) signature in Participation.signatureText.
   */
  Signed: v3_ParticipationSignature_Signed,
  /**
   * A signature for the service is required of this actor.
   */
  Required: v3_ParticipationSignature_Required,
};
/**
 *  Target that is substantially present in the service and which is directly affected by the service action (includes consumed material, devices, etc.).
 */
export const V3ParticipationTargetDirect = {
  /**
   * The target of an Observation action. Links an observation to a Role whose player is the substance or most specific component entity (material, micro-organism, etc.) being measured within the subject.
   *
   *
   *                            Examples: A "plasma porcelain substance concentration" has analyte a Role with player substance Entity "porcelain".
   *
   *
   *                            UsageNotes: The Role that this participation connects to may be any Role whose player is that substance measured. Very often, the scoper may indicate the system in which the component is being measured. E.g., for "plasma porcelain" the scoper could be "Plasma".
   */
  Analyte: v3_ParticipationType_Analyte,
  /**
   * In an obstetric service, the baby.
   */
  Baby: v3_ParticipationType_Baby,
  /**
   * The catalyst of a chemical reaction, such as an enzyme or a platinum surface. In biochemical reactions, connects the enzyme with the molecular interaction
   */
  Catalyst: v3_ParticipationType_Catalyst,
  /**
   * Participant material that is taken up, diminished, altered, or disappears in the act.
   */
  Consumable: v3_ParticipationType_Consumable,
  /**
   * Participant used in performing the act without being substantially affected by the act (i.e. durable or inert with respect to that particular service).
   *
   *
   *                            Examples: monitoring equipment, tools, but also access/drainage lines, prostheses, pace maker, etc.
   */
  Device: v3_ParticipationType_Device,
  /**
   * Target participant  that is substantially present in the act  and which is directly involved in the action (includes consumed material, devices, etc.).
   */
  DirectTarget: v3_ParticipationType_DirectTarget,
  /**
   * In some organ transplantation services and rarely in transfusion services a donor will be a target participant in the service.  However, in most cases transplantation is decomposed in three services: explantation, transport, and implantation.  The identity of the donor (recipient) is often irrelevant for the explantation (implantation) service.
   */
  Donor: v3_ParticipationType_Donor,
  /**
   * Description: The entity playing the associated role is the physical (including energy), chemical or biological substance that is participating in the exposure.  For example in communicable diseases, the associated playing entity is the disease causing pathogen.
   */
  ExposureAgent: v3_ParticipationType_ExposureAgent,
  /**
   * Description:Direct participation in an exposure act where it is unknown that the participant is the source or subject of the exposure.  If the participant is known to be the contact of an exposure then the SBJ participation type should be used.  If the participant is known to be the source then the EXSRC participation type should be used.
   */
  ExposureParticipation: v3_ParticipationType_ExposureParticipation,
  /**
   * Description: The entity playing the associated role is the target (contact) of exposure.
   */
  ExposureTarget: v3_ParticipationType_ExposureTarget,
  /**
   * Description:The entity playing the associated role is the source of exposure.
   */
  ExposureSource: v3_ParticipationType_ExposureSource,
  /**
   * A device that changes ownership due to the service, e.g., a pacemaker, a prosthesis, an insulin injection equipment (pen), etc.  Such material may need to be restocked after he service.
   */
  NonReuseableDevice: v3_ParticipationType_NonReuseableDevice,
  /**
   * Participant material that is brought forth (produced) in the act (e.g., specimen in a specimen collection, access or drainage in a placement service, medication package in a dispense service). It does not matter whether the material produced had existence prior to the service, or whether it is created in the service (e.g., in supply services the product is taken from a stock).
   */
  Product: v3_ParticipationType_Product,
  /**
   * A device that does not change ownership due to the service, i.e., a surgical instrument or tool or an endoscope.  The distinction between reuseable and non-reuseable must be made in order to know whether material must be re-stocked.
   */
  ReusableDevice: v3_ParticipationType_ReusableDevice,
  /**
   * The principle target on which the action happens.
   *
   *
   *                            Examples: The patient in physical examination, a specimen in a lab observation. May also be a patient's family member (teaching) or a device or room (cleaning, disinfecting, housekeeping).
   *
   *
   *                            UsageNotes: Not all direct targets are subjects. Consumables and devices used as tools for an act are not subjects. However, a device may be a subject of a maintenance action.
   */
  Subject: v3_ParticipationType_Subject,
  /**
   * The subject of non-clinical (e.g. laboratory) observation services is a specimen.
   */
  Specimen: v3_ParticipationType_Specimen,
  /**
   * Something incorporated in the subject of a therapy service to achieve a physiologic effect (e.g., heal, relieve, provoke a condition, etc.) on the subject.  In an administration service the therapeutic agent is a consumable, in a preparation or dispense service, it is a product.  Thus, consumable or product must be specified in accordance with the kind of service.
   */
  TherapeuticAgent: v3_ParticipationType_TherapeuticAgent,
};
/**
 *  The facility where the service is done.  May be a static building (or room therein) or a moving location (e.g., ambulance, helicopter, aircraft, train, truck, ship, etc.)
 */
export const V3ParticipationTargetLocation = {
  /**
   * The destination for services.  May be a static building (or room therein) or a movable facility (e.g., ship).
   */
  Destination: v3_ParticipationType_Destination,
  /**
   * A location where data about an Act was entered.
   */
  EntryLocation: v3_ParticipationType_EntryLocation,
  /**
   * The facility where the service is done.  May be a static building (or room therein) or a moving location (e.g., ambulance, helicopter, aircraft, train, truck, ship, etc.)
   */
  Location: v3_ParticipationType_Location,
  /**
   * The location of origin for services.  May be a static building (or room therein) or a movable facility (e.g., ship).
   */
  Origin: v3_ParticipationType_Origin,
  /**
   * Some services take place at multiple concurrent locations (e.g., telemedicine, telephone consultation).  The location where the principal performing actor is located is taken as the primary location (LOC) while the other location(s) are considered "remote."
   */
  Remote: v3_ParticipationType_Remote,
  /**
   * For services, an intermediate location that specifies a path between origin an destination.
   */
  Via: v3_ParticipationType_Via,
};
/**
 *  The principle target that the service acts on.  E.g. the patient in physical examination, a specimen in a lab observation. May also be a patient's family member (teaching) or a device or room (cleaning, disinfecting, housekeeping). Note: not all direct targets are subjects, consumables, and devices used as tools for a service are not subjects.  However, a device may be a subject of a maintenance service.
 */
export const V3ParticipationTargetSubject = {
  /**
   * The principle target on which the action happens.
   *
   *
   *                            Examples: The patient in physical examination, a specimen in a lab observation. May also be a patient's family member (teaching) or a device or room (cleaning, disinfecting, housekeeping).
   *
   *
   *                            UsageNotes: Not all direct targets are subjects. Consumables and devices used as tools for an act are not subjects. However, a device may be a subject of a maintenance action.
   */
  Subject: v3_ParticipationType_Subject,
  /**
   * The subject of non-clinical (e.g. laboratory) observation services is a specimen.
   */
  Specimen: v3_ParticipationType_Specimen,
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3ParticipationType = {
  /**
   * Participations related, but not primary to an act. The Referring, Admitting, and Discharging practitioners must be the same person as those authoring the ControlAct event for their respective trigger events.
   */
  ParticipationAncillary: v3_ParticipationType_ParticipationAncillary,
  /**
   * Parties that may or should contribute or have contributed information to the Act. Such information includes information leading to the decision to perform the Act and how to perform the Act (e.g., consultant), information that the Act itself seeks to reveal (e.g., informant of clinical history), or information about what Act was performed (e.g., informant witness).
   */
  ParticipationInformationGenerator: v3_ParticipationType_ParticipationInformationGenerator,
  /**
   * The practitioner who is responsible for admitting a patient to a patient encounter.
   */
  Admitter: v3_ParticipationType_Admitter,
  /**
   * The target of an Observation action. Links an observation to a Role whose player is the substance or most specific component entity (material, micro-organism, etc.) being measured within the subject.
   *
   *
   *                            Examples: A "plasma porcelain substance concentration" has analyte a Role with player substance Entity "porcelain".
   *
   *
   *                            UsageNotes: The Role that this participation connects to may be any Role whose player is that substance measured. Very often, the scoper may indicate the system in which the component is being measured. E.g., for "plasma porcelain" the scoper could be "Plasma".
   */
  Analyte: v3_ParticipationType_Analyte,
  /**
   * The practitioner that has responsibility for overseeing a patient's care during a patient encounter.
   */
  Attender: v3_ParticipationType_Attender,
  /**
   * Definition: A party that originates the Act and therefore has responsibility for the information given in the Act and ownership of this Act.
   *
   *
   *                            Example: the report writer, the person writing the act definition, the guideline author, the placer of an order, the EKG cart (device) creating a report etc. Every Act should have an author. Authorship is regardless of mood always actual authorship.
   *
   *                         Examples of such policies might include:
   *
   *
   *
   *                               The author and anyone they explicitly delegate may update the report;
   *
   *
   *
   *                               All administrators within the same clinic may cancel and reschedule appointments created by other administrators within that clinic;
   *
   *
   *
   *                         A party that is neither an author nor a party who is extended authorship maintenance rights by policy, may only amend, reverse, override, replace, or follow up in other ways on this Act, whereby the Act remains intact and is linked to another Act authored by that other party.
   */
  AuthorOriginator: v3_ParticipationType_AuthorOriginator,
  /**
   * A verifier who attests to the accuracy of an act, but who does not have privileges to legally authenticate the act. An example would be a resident physician who sees a patient and dictates a note, then later signs it. Their signature constitutes an authentication.
   */
  Authenticator: v3_ParticipationType_Authenticator,
  /**
   * In an obstetric service, the baby.
   */
  Baby: v3_ParticipationType_Baby,
  /**
   * Target on behalf of whom the service happens, but that is not necessarily present in the service.  Can occur together with direct target to indicate that a target is both, as in the case where the patient is the indirect beneficiary of a service rendered to a family member, e.g. counseling or given home care instructions.  This concept includes a participant, such as a covered party, who derives benefits from a service act covered by a coverage act.
   *
   *                         Note that the semantic role of the intended recipient who benefits from the happening denoted by the verb in the clause.  Thus, a patient who has no coverage under a policy or program may be a beneficiary of a health service while not being the beneficiary of coverage for that service.
   */
  Beneficiary: v3_ParticipationType_Beneficiary,
  /**
   * Definition: A factor, such as a microorganism, chemical substance, or form of radiation, whose presence, excessive presence, or (in deficiency diseases) relative absence is essential, in whole or in part, for the occurrence of a condition.
   *
   *                         Constraint:  The use of this participation is limited to observations.
   */
  CausativeAgent: v3_ParticipationType_CausativeAgent,
  /**
   * A person or organization who should be contacted for follow-up questions about the act in place of the author.
   */
  CallbackContact: v3_ParticipationType_CallbackContact,
  /**
   * The catalyst of a chemical reaction, such as an enzyme or a platinum surface. In biochemical reactions, connects the enzyme with the molecular interaction
   */
  Catalyst: v3_ParticipationType_Catalyst,
  /**
   * An advisor participating in the service by performing evaluations and making recommendations.
   */
  Consultant: v3_ParticipationType_Consultant,
  /**
   * The target participation for an individual in a health care coverage act in which the target role is either the policy holder of the coverage, or a covered party under the coverage.
   */
  CoverageTarget: v3_ParticipationType_CoverageTarget,
  /**
   * Participant material that is taken up, diminished, altered, or disappears in the act.
   */
  Consumable: v3_ParticipationType_Consumable,
  /**
   * An entity (person, organization or device) that is in charge of maintaining the information of this act (e.g., who maintains the report or the master service catalog item, etc.).
   */
  Custodian: v3_ParticipationType_Custodian,
  /**
   * Participant used in performing the act without being substantially affected by the act (i.e. durable or inert with respect to that particular service).
   *
   *
   *                            Examples: monitoring equipment, tools, but also access/drainage lines, prostheses, pace maker, etc.
   */
  Device: v3_ParticipationType_Device,
  /**
   * Target participant  that is substantially present in the act  and which is directly involved in the action (includes consumed material, devices, etc.).
   */
  DirectTarget: v3_ParticipationType_DirectTarget,
  /**
   * The practitioner who is responsible for the discharge of a patient from a patient encounter.
   */
  Discharger: v3_ParticipationType_Discharger,
  /**
   * Distributes material used in or generated during the act.
   */
  Distributor: v3_ParticipationType_Distributor,
  /**
   * In some organ transplantation services and rarely in transfusion services a donor will be a target participant in the service.  However, in most cases transplantation is decomposed in three services: explantation, transport, and implantation.  The identity of the donor (recipient) is often irrelevant for the explantation (implantation) service.
   */
  Donor: v3_ParticipationType_Donor,
  /**
   * The destination for services.  May be a static building (or room therein) or a movable facility (e.g., ship).
   */
  Destination: v3_ParticipationType_Destination,
  /**
   * A location where data about an Act was entered.
   */
  EntryLocation: v3_ParticipationType_EntryLocation,
  /**
   * A person entering the data into the originating system.  The data entry person is collected optionally for internal quality control purposes.  This includes the transcriptionist for dictated text.
   */
  DataEntryPerson: v3_ParticipationType_DataEntryPerson,
  /**
   * Only with Transportation services.  A person who escorts the patient.
   */
  Escort: v3_ParticipationType_Escort,
  /**
   * Description: The entity playing the associated role is the physical (including energy), chemical or biological substance that is participating in the exposure.  For example in communicable diseases, the associated playing entity is the disease causing pathogen.
   */
  ExposureAgent: v3_ParticipationType_ExposureAgent,
  /**
   * Description:Direct participation in an exposure act where it is unknown that the participant is the source or subject of the exposure.  If the participant is known to be the contact of an exposure then the SBJ participation type should be used.  If the participant is known to be the source then the EXSRC participation type should be used.
   */
  ExposureParticipation: v3_ParticipationType_ExposureParticipation,
  /**
   * Description: The entity playing the associated role is the target (contact) of exposure.
   */
  ExposureTarget: v3_ParticipationType_ExposureTarget,
  /**
   * Description:The entity playing the associated role is the source of exposure.
   */
  ExposureSource: v3_ParticipationType_ExposureSource,
  /**
   * The target person or organization contractually recognized by the issuer as a participant who has assumed fiscal responsibility for another personaTMs financial obligations by guaranteeing to pay for amounts owed to a particular account
   *
   *
   *                            Example:The subscriber of the patientaTMs health insurance policy signs a contract with the provider to be fiscally responsible for the patient billing account balance amount owed.
   */
  GuarantorParty: v3_ParticipationType_GuarantorParty,
  /**
   * Participant who posses an instrument such as a financial contract (insurance policy) usually based on some agreement with the author.
   */
  Holder: v3_ParticipationType_Holder,
  /**
   * Target that is not substantially present in the act and which is not directly affected by the act, but which will be a focus of the record or documentation of the act.
   */
  IndirectTarget: v3_ParticipationType_IndirectTarget,
  /**
   * A source of reported information (e.g., a next of kin who answers questions about the patient's history).  For history questions, the patient is logically an informant, yet the informant of history questions is implicitly the subject.
   */
  Informant: v3_ParticipationType_Informant,
  /**
   * A party, who may or should receive or who has recieved the Act or subsequent or derivative information of that Act. Information recipient is inert, i.e., independent of mood." Rationale: this is a generalization of a too diverse family that the definition can't be any more specific, and the concept is abstract so one of the specializations should be used.
   */
  InformationRecipient: v3_ParticipationType_InformationRecipient,
  /**
   * A verifier who legally authenticates the accuracy of an act. An example would be a staff physician who sees a patient and dictates a note, then later signs it. Their signature constitutes a legal authentication.
   */
  LegalAuthenticator: v3_ParticipationType_LegalAuthenticator,
  /**
   * The facility where the service is done.  May be a static building (or room therein) or a moving location (e.g., ambulance, helicopter, aircraft, train, truck, ship, etc.)
   */
  Location: v3_ParticipationType_Location,
  /**
   * An information recipient to notify for urgent matters about this Act. (e.g., in a laboratory order, critical results are being called by phone right away, this is the contact to call; or for an inpatient encounter, a next of kin to notify when the patient becomes critically ill).
   */
  UgentNotificationContact: v3_ParticipationType_UgentNotificationContact,
  /**
   * A device that changes ownership due to the service, e.g., a pacemaker, a prosthesis, an insulin injection equipment (pen), etc.  Such material may need to be restocked after he service.
   */
  NonReuseableDevice: v3_ParticipationType_NonReuseableDevice,
  /**
   * The location of origin for services.  May be a static building (or room therein) or a movable facility (e.g., ship).
   */
  Origin: v3_ParticipationType_Origin,
  /**
   * Indicates that the target of the participation is involved in some manner in the act, but does not qualify how.
   */
  Participation: v3_ParticipationType_Participation,
  /**
   * The principal or primary performer of the act.
   */
  PrimaryPerformer: v3_ParticipationType_PrimaryPerformer,
  /**
   * Information recipient to whom an act statement is primarily directed. E.g., a primary care provider receiving a discharge letter from a hospitalist, a health department receiving information on a suspected case of infectious disease. Multiple of these participations may exist on the same act without requiring that recipients be ranked as primary vs. secondary.
   */
  PrimaryInformationRecipient: v3_ParticipationType_PrimaryInformationRecipient,
  /**
   * Participant material that is brought forth (produced) in the act (e.g., specimen in a specimen collection, access or drainage in a placement service, medication package in a dispense service). It does not matter whether the material produced had existence prior to the service, or whether it is created in the service (e.g., in supply services the product is taken from a stock).
   */
  Product: v3_ParticipationType_Product,
  /**
   * Definition: A person, non-person living subject, organization or device that who actually and principally carries out the action. Device should only be assigned as a performer in circumstances where the device is performing independent of human intervention.  Need not be the principal responsible actor.
   *
   *
   *                            Exampe: A surgery resident operating under supervision of attending surgeon, a search and rescue dog locating survivors, an electronic laboratory analyzer or the laboratory discipline requested to perform a laboratory test. The performer may also be the patient in self-care, e.g. fingerstick blood sugar. The traditional order filler is a performer. This information should accompany every service event.
   *
   *
   *                            Note: that existing HL7 designs assign an organization as the playing entity of the Role that is the performer.  These designs should be revised in subsequent releases to make this the scooping entity for the role involved.
   */
  Performer: v3_ParticipationType_Performer,
  /**
   * The record target indicates whose medical record holds the documentation of this act.  This is especially important when the subject of a service is not the patient himself.
   */
  RecordTarget: v3_ParticipationType_RecordTarget,
  /**
   * The person (or organization) who receives the product of an Act.
   */
  Receiver: v3_ParticipationType_Receiver,
  /**
   * A device that does not change ownership due to the service, i.e., a surgical instrument or tool or an endoscope.  The distinction between reuseable and non-reuseable must be made in order to know whether material must be re-stocked.
   */
  ReusableDevice: v3_ParticipationType_ReusableDevice,
  /**
   * A person having referred the subject of the service to the performer (referring physician).  Typically, a referring physician will receive a report.
   */
  Referrer: v3_ParticipationType_Referrer,
  /**
   * A participant (e.g. provider) who has referred the subject of an act (e.g. patient).
   *
   *                         Typically, a referred by participant will provide a report (e.g. referral).
   */
  ReferredBy: v3_ParticipationType_ReferredBy,
  /**
   * The person who receives the patient
   */
  ReferredTo: v3_ParticipationType_ReferredTo,
  /**
   * The person or organization that has primary responsibility for the act.  The responsible party is not necessarily present in an action, but is accountable for the action through the power to delegate, and the duty to review actions with the performing actor after the fact.  This responsibility may be ethical, legal, contractual, fiscal, or fiduciary in nature.
   *
   *
   *                            Example: A person who is the head of a biochemical laboratory; a sponsor for a policy or government program.
   */
  ResponsibleParty: v3_ParticipationType_ResponsibleParty,
  /**
   * Some services take place at multiple concurrent locations (e.g., telemedicine, telephone consultation).  The location where the principal performing actor is located is taken as the primary location (LOC) while the other location(s) are considered "remote."
   */
  Remote: v3_ParticipationType_Remote,
  /**
   * The principle target on which the action happens.
   *
   *
   *                            Examples: The patient in physical examination, a specimen in a lab observation. May also be a patient's family member (teaching) or a device or room (cleaning, disinfecting, housekeeping).
   *
   *
   *                            UsageNotes: Not all direct targets are subjects. Consumables and devices used as tools for an act are not subjects. However, a device may be a subject of a maintenance action.
   */
  Subject: v3_ParticipationType_Subject,
  /**
   * The subject of non-clinical (e.g. laboratory) observation services is a specimen.
   */
  Specimen: v3_ParticipationType_Specimen,
  /**
   * A person assisting in an act through his substantial presence and involvement   This includes: assistants, technicians, associates, or whatever the job titles may be.
   */
  SecondaryPerformer: v3_ParticipationType_SecondaryPerformer,
  /**
   * Something incorporated in the subject of a therapy service to achieve a physiologic effect (e.g., heal, relieve, provoke a condition, etc.) on the subject.  In an administration service the therapeutic agent is a consumable, in a preparation or dispense service, it is a product.  Thus, consumable or product must be specified in accordance with the kind of service.
   */
  TherapeuticAgent: v3_ParticipationType_TherapeuticAgent,
  /**
   * An entity entering the data into the originating system. The data entry entity is collected optionally for internal quality control purposes. This includes the transcriptionist for dictated text transcribed into electronic form.
   */
  Transcriber: v3_ParticipationType_Transcriber,
  /**
   * A secondary information recipient, who receives copies (e.g., a primary care provider receiving copies of results as ordered by specialist).
   */
  Tracker: v3_ParticipationType_Tracker,
  /**
   * For services, an intermediate location that specifies a path between origin an destination.
   */
  Via: v3_ParticipationType_Via,
  /**
   * A person who verifies the correctness and appropriateness of the service (plan, order, event, etc.) and hence takes on accountability.
   */
  Verifier: v3_ParticipationType_Verifier,
  /**
   * Only with service events.  A person witnessing the action happening without doing anything.  A witness is not necessarily aware, much less approves of anything stated in the service event.  Example for a witness is students watching an operation or an advanced directive witness.
   */
  Witness: v3_ParticipationType_Witness,
};
/**
 *  A person who verifies the correctness and appropriateness of the service (plan, order, event, etc.) and hence takes on accountability.
 */
export const V3ParticipationVerifier = {
  /**
   * A verifier who attests to the accuracy of an act, but who does not have privileges to legally authenticate the act. An example would be a resident physician who sees a patient and dictates a note, then later signs it. Their signature constitutes an authentication.
   */
  Authenticator: v3_ParticipationType_Authenticator,
  /**
   * A verifier who legally authenticates the accuracy of an act. An example would be a staff physician who sees a patient and dictates a note, then later signs it. Their signature constitutes a legal authentication.
   */
  LegalAuthenticator: v3_ParticipationType_LegalAuthenticator,
  /**
   * A person who verifies the correctness and appropriateness of the service (plan, order, event, etc.) and hence takes on accountability.
   */
  Verifier: v3_ParticipationType_Verifier,
};
const v3_PatientImportance_BoardMember: Coding = {
  code: 'BM',
  display: 'Board Member',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PatientImportance',
};
const v3_PatientImportance_PhysicianFamilyMember: Coding = {
  code: 'DFM',
  display: 'Physician Family Member',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PatientImportance',
};
const v3_PatientImportance_StaffPhysician: Coding = {
  code: 'DR',
  display: 'Staff Physician',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PatientImportance',
};
const v3_PatientImportance_FinancialDonor: Coding = {
  code: 'FD',
  display: 'Financial Donor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PatientImportance',
};
const v3_PatientImportance_ForeignDignitary: Coding = {
  code: 'FOR',
  display: 'Foreign Dignitary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PatientImportance',
};
const v3_PatientImportance_GovernmentDignitary: Coding = {
  code: 'GOVT',
  display: 'Government Dignitary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PatientImportance',
};
const v3_PatientImportance_StaffFamilyMember: Coding = {
  code: 'SFM',
  display: 'Staff Family Member',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PatientImportance',
};
const v3_PatientImportance_StaffMember: Coding = {
  code: 'STF',
  display: 'Staff Member',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PatientImportance',
};
const v3_PatientImportance_VeryImportantPerson: Coding = {
  code: 'VIP',
  display: 'Very Important Person',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PatientImportance',
};
/**
 *  Patient VIP code
 */
export const V3PatientImportance = {
  /**
   * Board member of health care organization
   */
  BoardMember: v3_PatientImportance_BoardMember,
  /**
   * Family member of staff physician
   */
  PhysicianFamilyMember: v3_PatientImportance_PhysicianFamilyMember,
  /**
   * Member of the health care organization physician staff
   */
  StaffPhysician: v3_PatientImportance_StaffPhysician,
  /**
   * Financial donor to the health care organization
   */
  FinancialDonor: v3_PatientImportance_FinancialDonor,
  /**
   * Foreign citizen dignitary of interest to the health care organization
   */
  ForeignDignitary: v3_PatientImportance_ForeignDignitary,
  /**
   * Government dignitary of interest to the organization
   */
  GovernmentDignitary: v3_PatientImportance_GovernmentDignitary,
  /**
   * Family member of staff member
   */
  StaffFamilyMember: v3_PatientImportance_StaffFamilyMember,
  /**
   * Staff member of the health care organization
   */
  StaffMember: v3_PatientImportance_StaffMember,
  /**
   * Very important person of interest to the health care organization
   */
  VeryImportantPerson: v3_PatientImportance_VeryImportantPerson,
};
const v3_PaymentTerms_CashOnDelivery: Coding = {
  code: 'COD',
  display: 'Cash on Delivery',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PaymentTerms',
};
const v3_PaymentTerms_Net30Days: Coding = {
  code: 'N30',
  display: 'Net 30 days',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PaymentTerms',
};
const v3_PaymentTerms_Net60Days: Coding = {
  code: 'N60',
  display: 'Net 60 days',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PaymentTerms',
};
const v3_PaymentTerms_Net90Days: Coding = {
  code: 'N90',
  display: 'Net 90 days',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PaymentTerms',
};
/**
 *  Describes payment terms for a financial transaction, used in an invoice. This is typically expressed as a responsibility of the acceptor or payor of an invoice.
 */
export const V3PaymentTerms = {
  /**
   * Payment in full for products and/or services is required as soon as the service is performed or goods delivered.
   */
  CashOnDelivery: v3_PaymentTerms_CashOnDelivery,
  /**
   * Payment in full for products and/or services is required 30 days from the time the service is performed or goods delivered.
   */
  Net30Days: v3_PaymentTerms_Net30Days,
  /**
   * Payment in full for products and/or services is required 60 days from the time the service is performed or goods delivered.
   */
  Net60Days: v3_PaymentTerms_Net60Days,
  /**
   * Payment in full for products and/or services is required 90 days from the time the service is performed or goods delivered.
   */
  Net90Days: v3_PaymentTerms_Net90Days,
};
/**
 *  Types of personal relationships between two living subjects.  Example:
 * Parent, sibling, unrelated friend, neighbor
 */
export const V3PersonalRelationshipRoleType = {
  /**
   * The player of the role (father) is a male who has taken the scoper (child) into their family through legal means and raises them as his own child.
   */
  AdoptiveFather: v3_RoleCode_AdoptiveFather,
  /**
   * The player of the role (father) is a female who has taken the scoper (child) into their family through legal means and raises them as her own child.
   */
  AdoptiveMother: v3_RoleCode_AdoptiveMother,
  /**
   * The player of the role (parent) has taken the scoper (child) into their family through legal means and raises them as his or her own child.
   */
  AdoptiveParent: v3_RoleCode_AdoptiveParent,
  /**
   * The player of the role is a sister of the scoping person's mother or father.
   */
  Aunt: v3_RoleCode_Aunt,
  /**
   * The player of the role is a male sharing one or both parents in common with the scoping entity.
   */
  Brother: v3_RoleCode_Brother,
  /**
   * The player of the role is: (1) a brother of the scoping person's spouse, or (2) the husband of the scoping person's sister, or (3) the husband of a sister of the scoping person's spouse.
   */
  BrotherInLaw: v3_RoleCode_BrotherInLaw,
  /**
   * The player of the role is a child of the scoping entity.
   */
  Child: v3_RoleCode_Child,
  /**
   * The player of the role is a child taken into a family through legal means and raised by the scoping person (parent) as his or her own child.
   */
  AdoptedChild: v3_RoleCode_AdoptedChild,
  /**
   * The player of the role is a child receiving parental care and nurture from the scoping person (parent) but not related to him or her through legal or blood ties.
   */
  FosterChild: v3_RoleCode_FosterChild,
  /**
   * The player of the role is the spouse of scoping person's child.
   */
  ChildInLaw: v3_RoleCode_ChildInLaw,
  /**
   * The player of the role is a relative of the scoping person descended from a common ancestor, such as a 	grandparent, by two or more steps in a diverging line.
   */
  Cousin: v3_RoleCode_Cousin,
  /**
   * The player of the role is a female offspring of the scoping entity (parent).
   */
  NaturalDaughter: v3_RoleCode_NaturalDaughter,
  /**
   * The player of the role is a female child taken into a family through legal means and raised by the scoping person (parent) as his or her own child.
   */
  AdoptedDaughter: v3_RoleCode_AdoptedDaughter,
  /**
   * Description: The player of the role is a female child (of any type) of scoping entity (parent)
   */
  Daughter: v3_RoleCode_Daughter,
  /**
   * The player of the role is a female child receiving parental care and nurture from the scoping person (parent) but not related to him or her through legal or blood ties.
   */
  FosterDaughter: v3_RoleCode_FosterDaughter,
  /**
   * The player of the role is the wife of scoping person's son.
   */
  DaughterInLaw: v3_RoleCode_DaughterInLaw,
  /**
   * The player of the role cohabits with the scoping person but is not the scoping person's spouse.
   */
  DomesticPartner: v3_RoleCode_DomesticPartner,
  /**
   * Description: A family member not having an immediate genetic or legal relationship e.g. Aunt, cousin, great grandparent, grandchild, grandparent, niece, nephew or uncle.
   */
  ExtendedFamilyMember: v3_RoleCode_ExtendedFamilyMember,
  /**
   * A relationship between two people characterizing their "familial" relationship
   */
  FamilyMember: v3_RoleCode_FamilyMember,
  /**
   * Player of the role was previously joined to the scoping person in marriage and this marriage is now dissolved and inactive.
   *
   *
   *                            Usage Note: This is significant to indicate as some jurisdictions have different legal requirements for former spouse to access the patient's record, from a general friend.
   */
  FormerSpouse: v3_RoleCode_FormerSpouse,
  /**
   * The player of the role is a person who is known, liked, and trusted by the scoping person.
   */
  UnrelatedFriend: v3_RoleCode_UnrelatedFriend,
  /**
   * The player of the role is a male who begets or raises or nurtures the scoping entity (child).
   */
  Father: v3_RoleCode_Father,
  /**
   * The player of the role (parent) who is a male state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterFather: v3_RoleCode_FosterFather,
  /**
   * The player of the role is the father of the scoping person's husband or wife.
   */
  FatherInLaw: v3_RoleCode_FatherInLaw,
  /**
   * The scoper and player were carried in the same womb and share common biological parents but are the product of distinct egg/sperm pairs.
   */
  FraternalTwin: v3_RoleCode_FraternalTwin,
  /**
   * The scoper was carried in the same womb as the male player and shares common biological parents but is the product of a distinct egg/sperm pair.
   */
  FraternalTwinBrother: v3_RoleCode_FraternalTwinBrother,
  /**
   * The scoper was carried in the same womb as the female player and shares common biological parents but is the product of a distinct egg/sperm pair.
   */
  FraternalTwinSister: v3_RoleCode_FraternalTwinSister,
  /**
   * The player is a female whose womb carries the fetus of the scoper.  Generally used when the gestational mother and natural mother are not the same.
   */
  GestationalMother: v3_RoleCode_GestationalMother,
  /**
   * The player of the role is the father of the scoping person's grandparent.
   */
  GreatGrandfather: v3_RoleCode_GreatGrandfather,
  /**
   * The player of the role is the mother of the scoping person's grandparent.
   */
  GreatGrandmother: v3_RoleCode_GreatGrandmother,
  /**
   * The player of the role is a parent of the scoping person's grandparent.
   */
  GreatGrandparent: v3_RoleCode_GreatGrandparent,
  /**
   * The player of the role is the father of the scoping person's mother or father.
   */
  Grandfather: v3_RoleCode_Grandfather,
  /**
   * The player of the role is the mother of the scoping person's mother or father.
   */
  Grandmother: v3_RoleCode_Grandmother,
  /**
   * The player of the role is a child of the scoping person's son or daughter.
   */
  Grandchild: v3_RoleCode_Grandchild,
  /**
   * The player of the role is a daughter of the scoping person's son or daughter.
   */
  Granddaughter: v3_RoleCode_Granddaughter,
  /**
   * The player of the role is a son of the scoping person's son or daughter.
   */
  Grandson: v3_RoleCode_Grandson,
  /**
   * The player of the role is a parent of the scoping person's mother or father.
   */
  Grandparent: v3_RoleCode_Grandparent,
  /**
   * The player of the role is a male related to the scoping entity by sharing only one biological parent.
   */
  HalfBrother: v3_RoleCode_HalfBrother,
  /**
   * The player of the role is related to the scoping entity by sharing only one biological parent.
   */
  HalfSibling: v3_RoleCode_HalfSibling,
  /**
   * The player of the role is a female related to the scoping entity by sharing only one biological parent.
   */
  HalfSister: v3_RoleCode_HalfSister,
  /**
   * The player of the role is a man joined to a woman (scoping person) in marriage.
   */
  Husband: v3_RoleCode_Husband,
  /**
   * A relationship between an individual and a member of their spousal partner's immediate family.
   */
  Inlaw: v3_RoleCode_Inlaw,
  /**
   * The scoper and player are offspring of the same egg-sperm pair.
   */
  IdenticalTwin: v3_RoleCode_IdenticalTwin,
  /**
   * The male scoper is an offspring of the same egg-sperm pair as the male player.
   */
  IdenticalTwinBrother: v3_RoleCode_IdenticalTwinBrother,
  /**
   * The female scoper is an offspring of the same egg-sperm pair as the female player.
   */
  IdenticalTwinSister: v3_RoleCode_IdenticalTwinSister,
  /**
   * Description:The player of the role is a biological sister of the scoping person's biological mother.
   */
  MaternalAunt: v3_RoleCode_MaternalAunt,
  /**
   * Description:The player of the role is a biological relative of the scoping person descended from a common ancestor on the player's mother's side, such as a grandparent, by two or more steps in a diverging line.
   */
  MaternalCousin: v3_RoleCode_MaternalCousin,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological mother's parent.
   */
  MaternalGreatGrandfather: v3_RoleCode_MaternalGreatGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological mother's parent.
   */
  MaternalGreatGrandmother: v3_RoleCode_MaternalGreatGrandmother,
  /**
   * Description:The player of the role is a biological parent of the scoping person's biological mother's parent.
   */
  MaternalGreatGrandparent: v3_RoleCode_MaternalGreatGrandparent,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological mother.
   */
  MaternalGrandfather: v3_RoleCode_MaternalGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological mother.
   */
  MaternalGrandmother: v3_RoleCode_MaternalGrandmother,
  /**
   * Description:The player of the role is the biological parent of the scoping person's biological mother.
   */
  MaternalGrandparent: v3_RoleCode_MaternalGrandparent,
  /**
   * The player of the role is a female who conceives, gives birth to, or raises and nurtures the scoping entity (child).
   */
  Mother: v3_RoleCode_Mother,
  /**
   * The player of the role (parent) who is a female state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterMother: v3_RoleCode_FosterMother,
  /**
   * The player of the role is the mother of the scoping person's husband or wife.
   */
  MotherInLaw: v3_RoleCode_MotherInLaw,
  /**
   * Description:The player of the role is a biological brother of the scoping person's biological mother.
   */
  MaternalUncle: v3_RoleCode_MaternalUncle,
  /**
   * The player of the role lives near or next to the 	scoping person.
   */
  Neighbor: v3_RoleCode_Neighbor,
  /**
   * The player of the role is a male having the same biological parents as the scoping entity.
   */
  NaturalBrother: v3_RoleCode_NaturalBrother,
  /**
   * The player of the role is an offspring of the scoping entity as determined by birth.
   */
  NaturalChild: v3_RoleCode_NaturalChild,
  /**
   * The player of the role is a son of the scoping person's brother or sister or of the brother or sister of the 	scoping person's spouse.
   */
  Nephew: v3_RoleCode_Nephew,
  /**
   * The player of the role is a male who begets the scoping entity (child).
   */
  NaturalFather: v3_RoleCode_NaturalFather,
  /**
   * Indicates the biologic male parent of a fetus.
   */
  NaturalFatherOfFetus: v3_RoleCode_NaturalFatherOfFetus,
  /**
   * The player of the role is a daughter of the scoping person's brother or sister or of the brother or sister of the 	scoping person's spouse.
   */
  Niece: v3_RoleCode_Niece,
  /**
   * The player of the role is a child of scoping person's brother or sister or of the brother or sister of the 	scoping person's spouse.
   */
  NieceNephew: v3_RoleCode_NieceNephew,
  /**
   * The player of the role is a female who conceives or gives birth to the scoping entity (child).
   */
  NaturalMother: v3_RoleCode_NaturalMother,
  /**
   * The player is the biologic female parent of the scoping fetus.
   */
  NaturalMotherOfFetus: v3_RoleCode_NaturalMotherOfFetus,
  /**
   * natural parent
   */
  NaturalParent: v3_RoleCode_NaturalParent,
  /**
   * The player of the role has both biological parents in common with the scoping entity.
   */
  NaturalSibling: v3_RoleCode_NaturalSibling,
  /**
   * The player of the role is a female having the same biological parents as the scoping entity.
   */
  NaturalSister: v3_RoleCode_NaturalSister,
  /**
   * The relationship that a person has with his or her self.
   */
  Self: v3_RoleCode_Self,
  /**
   * Description:The player of the role is a biological sister of the scoping person's biological father.
   */
  PaternalAunt: v3_RoleCode_PaternalAunt,
  /**
   * Description:The player of the role is a biological relative of the scoping person descended from a common ancestor on the player's father's side, such as a grandparent, by two or more steps in a diverging line.
   */
  PaternalCousin: v3_RoleCode_PaternalCousin,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological father's parent.
   */
  PaternalGreatGrandfather: v3_RoleCode_PaternalGreatGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological father's parent.
   */
  PaternalGreatGrandmother: v3_RoleCode_PaternalGreatGrandmother,
  /**
   * Description:The player of the role is a biological parent of the scoping person's biological father's parent.
   */
  PaternalGreatGrandparent: v3_RoleCode_PaternalGreatGrandparent,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological father.
   */
  PaternalGrandfather: v3_RoleCode_PaternalGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological father.
   */
  PaternalGrandmother: v3_RoleCode_PaternalGrandmother,
  /**
   * Description:The player of the role is the biological parent of the scoping person's biological father.
   */
  PaternalGrandparent: v3_RoleCode_PaternalGrandparent,
  /**
   * The player of the role is one who begets, gives birth to, or nurtures and raises the scoping entity (child).
   */
  Parent: v3_RoleCode_Parent,
  /**
   * The player of the role (parent) who is a state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterParent: v3_RoleCode_FosterParent,
  /**
   * The player of the role is the parent of scoping person's husband or wife.
   */
  ParentInLaw: v3_RoleCode_ParentInLaw,
  /**
   * Description:The player of the role is a biological brother of the scoping person's biological father.
   */
  PaternalUncle: v3_RoleCode_PaternalUncle,
  /**
   * One who shares living quarters with the subject.
   */
  Roommate: v3_RoleCode_Roommate,
  /**
   * The player of the role shares one or both parents in common with the scoping entity.
   */
  Sibling: v3_RoleCode_Sibling,
  /**
   * The player of the role is: (1) a sibling of the scoping person's spouse, or (2) the spouse of the scoping person's sibling, or (3) the spouse of a sibling of the scoping person's spouse.
   */
  SiblingInLaw: v3_RoleCode_SiblingInLaw,
  /**
   * A person who is important to one's well being; especially a spouse or one in a similar relationship.  (The player is the one who is important)
   */
  SignificantOther: v3_RoleCode_SignificantOther,
  /**
   * The player of the role is a female sharing one or both parents in common with the scoping entity.
   */
  Sister: v3_RoleCode_Sister,
  /**
   * The player of the role is: (1) a sister of the scoping person's spouse, or (2) the wife of the scoping person's brother, or (3) the wife of a brother of the scoping person's spouse.
   */
  SisterInLaw: v3_RoleCode_SisterInLaw,
  /**
   * The player of the role is a male offspring of the scoping entity (parent).
   */
  NaturalSon: v3_RoleCode_NaturalSon,
  /**
   * The player of the role is a male child taken into a family through legal means and raised by the scoping person (parent) as his or her own child.
   */
  AdoptedSon: v3_RoleCode_AdoptedSon,
  /**
   * Description: The player of the role is a male child (of any type) of scoping entity (parent)
   */
  Son: v3_RoleCode_Son,
  /**
   * The player of the role is a male child receiving parental care and nurture from the scoping person (parent) but not related to him or her through legal or blood ties.
   */
  FosterSon: v3_RoleCode_FosterSon,
  /**
   * The player of the role is the husband of scoping person's daughter.
   */
  SonInLaw: v3_RoleCode_SonInLaw,
  /**
   * The player of the role is a marriage partner of the scoping person.
   */
  Spouse: v3_RoleCode_Spouse,
  /**
   * The player of the role is a son of the scoping person's stepparent.
   */
  Stepbrother: v3_RoleCode_Stepbrother,
  /**
   * The player of the role is a child of the scoping person's spouse by a previous union.
   */
  StepChild: v3_RoleCode_StepChild,
  /**
   * The player of the role is a daughter of the scoping person's spouse by a previous union.
   */
  Stepdaughter: v3_RoleCode_Stepdaughter,
  /**
   * The player of the role is the husband of scoping person's mother and not the scoping person's natural father.
   */
  Stepfather: v3_RoleCode_Stepfather,
  /**
   * The player of the role is the wife of scoping person's father and not the scoping person's natural mother.
   */
  Stepmother: v3_RoleCode_Stepmother,
  /**
   * The player of the role is the spouse of the scoping person's parent and not the scoping person's natural parent.
   */
  StepParent: v3_RoleCode_StepParent,
  /**
   * The player of the role is a child of the scoping person's stepparent.
   */
  StepSibling: v3_RoleCode_StepSibling,
  /**
   * The player of the role is a daughter of the scoping person's stepparent.
   */
  Stepsister: v3_RoleCode_Stepsister,
  /**
   * The player of the role is a son of the scoping person's spouse by a previous union.
   */
  Stepson: v3_RoleCode_Stepson,
  /**
   * The scoper and player were carried in the same womb and shared common biological parents.
   */
  Twin: v3_RoleCode_Twin,
  /**
   * The scoper was carried in the same womb as the male player and shares common biological parents.
   */
  TwinBrother: v3_RoleCode_TwinBrother,
  /**
   * The scoper was carried in the same womb as the female player and shares common biological parents.
   */
  TwinSister: v3_RoleCode_TwinSister,
  /**
   * The player of the role is a brother of the scoping person's mother or father.
   */
  Uncle: v3_RoleCode_Uncle,
  /**
   * The player of the role is a woman joined to a man (scoping person) in marriage.
   */
  Wife: v3_RoleCode_Wife,
};
const v3_PersonDisabilityType_VisionImpaired: Coding = {
  code: '1',
  display: 'Vision impaired',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PersonDisabilityType',
};
const v3_PersonDisabilityType_HearingImpaired: Coding = {
  code: '2',
  display: 'Hearing impaired',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PersonDisabilityType',
};
const v3_PersonDisabilityType_SpeechImpaired: Coding = {
  code: '3',
  display: 'Speech impaired',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PersonDisabilityType',
};
const v3_PersonDisabilityType_MentallyImpaired: Coding = {
  code: '4',
  display: 'Mentally impaired',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PersonDisabilityType',
};
const v3_PersonDisabilityType_MobilityImpaired: Coding = {
  code: '5',
  display: 'Mobility impaired',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PersonDisabilityType',
};
const v3_PersonDisabilityType_RequiresCrib: Coding = {
  code: 'CB',
  display: 'Requires crib',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PersonDisabilityType',
};
const v3_PersonDisabilityType_RequiresCrutches: Coding = {
  code: 'CR',
  display: 'Requires crutches',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PersonDisabilityType',
};
const v3_PersonDisabilityType_RequiresGurney: Coding = {
  code: 'G',
  display: 'Requires gurney',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PersonDisabilityType',
};
const v3_PersonDisabilityType_RequiresWheelchair: Coding = {
  code: 'WC',
  display: 'Requires wheelchair',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PersonDisabilityType',
};
const v3_PersonDisabilityType_RequiresWalker: Coding = {
  code: 'WK',
  display: 'Requires walker',
  system: 'http://terminology.hl7.org/CodeSystem/v3-PersonDisabilityType',
};
/**
 *  A code identifying a person's disability.
 */
export const V3PersonDisabilityType = {
  /**
   * Vision impaired
   */
  VisionImpaired: v3_PersonDisabilityType_VisionImpaired,
  /**
   * Hearing impaired
   */
  HearingImpaired: v3_PersonDisabilityType_HearingImpaired,
  /**
   * Speech impaired
   */
  SpeechImpaired: v3_PersonDisabilityType_SpeechImpaired,
  /**
   * Mentally impaired
   */
  MentallyImpaired: v3_PersonDisabilityType_MentallyImpaired,
  /**
   * Mobility impaired
   */
  MobilityImpaired: v3_PersonDisabilityType_MobilityImpaired,
  /**
   * A crib is required to move the person
   */
  RequiresCrib: v3_PersonDisabilityType_RequiresCrib,
  /**
   * Person requires crutches to ambulate
   */
  RequiresCrutches: v3_PersonDisabilityType_RequiresCrutches,
  /**
   * A gurney is required to move the person
   */
  RequiresGurney: v3_PersonDisabilityType_RequiresGurney,
  /**
   * Person requires a wheelchair to be ambulatory
   */
  RequiresWheelchair: v3_PersonDisabilityType_RequiresWheelchair,
  /**
   * Person requires a walker to ambulate
   */
  RequiresWalker: v3_PersonDisabilityType_RequiresWalker,
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3ProbabilityDistributionType = {
  /**
   * The beta-distribution is used for data that is bounded on both sides and may or may not be skewed (e.g., occurs when probabilities are estimated.)  Two parameters a and b  are available to adjust the curve.  The mean m and variance s2 relate as follows: m = a/ (a + b) and s2 = ab/((a + b)2 (a + b + 1)).
   */
  Beta: v3_ProbabilityDistributionType_Beta,
  /**
   * Used for data that describes extinction.  The exponential distribution is a special form of g-distribution where a = 1, hence, the relationship to mean m and variance s2 are m = b and s2 = b2.
   */
  Exponential: v3_ProbabilityDistributionType_Exponential,
  /**
   * Used to describe the quotient of two c2 random variables.  The F-distribution has two parameters n1 and n2, which are the numbers of degrees of freedom of the numerator and denominator variable respectively. The relationship to mean m  and variance s2 are: m = n2 / (n2 - 2) and s2 = (2 n2 (n2 + n1 - 2)) / (n1 (n2 - 2)2 (n2 - 4)).
   */
  F: v3_ProbabilityDistributionType_F,
  /**
   * The gamma-distribution used for data that is skewed and bounded to the right, i.e. where the maximum of the distribution curve is located near the origin.  The g-distribution has a two parameters a and b.  The relationship to mean m and variance s2 is m = a b and s2 = a b2.
   */
  Gamma: v3_ProbabilityDistributionType_Gamma,
  /**
   * The logarithmic normal distribution is used to transform skewed random variable X into a normally distributed random variable U = log X. The log-normal distribution can be specified with the properties mean m and standard deviation s.  Note however that mean m and standard deviation s are the parameters of the raw value distribution, not the transformed parameters of the lognormal distribution that are conventionally referred to by the same letters.  Those log-normal parameters mlog and slog relate to the mean m and standard deviation s of the data value through slog2 = log (s2/m2 + 1) and mlog = log m - slog2/2.
   */
  LogNormal: v3_ProbabilityDistributionType_LogNormal,
  /**
   * This is the well-known bell-shaped normal distribution.  Because of the central limit theorem, the normal distribution is the distribution of choice for an unbounded random variable that is an outcome of a combination of many stochastic processes.  Even for values bounded on a single side (i.e. greater than 0) the normal distribution may be accurate enough if the mean is "far away" from the bound of the scale measured in terms of standard deviations.
   */
  NormalGaussian: v3_ProbabilityDistributionType_NormalGaussian,
  /**
   * Used to describe the quotient of a normal random variable and the square root of a c2 random variable.  The t-distribution has one parameter n, the number of degrees of freedom. The relationship to mean m  and variance s2 are: m = 0 and s2 = n / (n - 2)
   */
  T: v3_ProbabilityDistributionType_T,
  /**
   * The uniform distribution assigns a constant probability over the entire interval of possible outcomes, while all outcomes outside this interval are assumed to have zero probability.  The width of this interval is 2s sqrt(3).  Thus, the uniform distribution assigns the probability densities f(x) = sqrt(2 s sqrt(3))  to values m - s sqrt(3) >= x <= m + s sqrt(3) and f(x) = 0 otherwise.
   */
  Uniform: v3_ProbabilityDistributionType_Uniform,
  /**
   * Used to describe the sum of squares of random variables which occurs when a variance is estimated (rather than presumed) from the sample.  The only parameter of the c2-distribution is n, so called the number of degrees of freedom (which is the number of independent parts in the sum).  The c2-distribution is a special type of g-distribution with parameter a = n /2 and b  = 2.  Hence, m = n and s2 = 2 n.
   */
  ChiSquare: v3_ProbabilityDistributionType_ChiSquare,
};
const v3_ProcessingID_Debugging: Coding = {
  code: 'D',
  display: 'Debugging',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProcessingID',
};
const v3_ProcessingID_Production: Coding = {
  code: 'P',
  display: 'Production',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProcessingID',
};
const v3_ProcessingID_Training: Coding = {
  code: 'T',
  display: 'Training',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProcessingID',
};
/**
 *  Codes used to specify whether a message is part of a production, training, or debugging system.
 */
export const V3ProcessingID = {
  /**
   * Identifies debugging type of processing.
   */
  Debugging: v3_ProcessingID_Debugging,
  /**
   * Identifies production type of processing.
   */
  Production: v3_ProcessingID_Production,
  /**
   * Identifies training type of processing.
   */
  Training: v3_ProcessingID_Training,
};
const v3_ProcessingMode_Archive: Coding = {
  code: 'A',
  display: 'Archive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProcessingMode',
};
const v3_ProcessingMode_InitialLoad: Coding = {
  code: 'I',
  display: 'Initial load',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProcessingMode',
};
const v3_ProcessingMode_RestoreFromArchive: Coding = {
  code: 'R',
  display: 'Restore from archive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProcessingMode',
};
const v3_ProcessingMode_CurrentProcessing: Coding = {
  code: 'T',
  display: 'Current processing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ProcessingMode',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3ProcessingMode = {
  /**
   * Identifies archive mode of processing.
   */
  Archive: v3_ProcessingMode_Archive,
  /**
   * Identifies initial load mode of processing.
   */
  InitialLoad: v3_ProcessingMode_InitialLoad,
  /**
   * Identifies restore mode of processing.
   */
  RestoreFromArchive: v3_ProcessingMode_RestoreFromArchive,
  /**
   * Identifies on-line mode of processing.
   */
  CurrentProcessing: v3_ProcessingMode_CurrentProcessing,
};
/**
 *  Specifies the state change of a target Act, using ActStatus codes, from its previous state as a predecessor Act. For example, if the target Act is the result of a predecessor Act being "obsoleted" and replaced with the target Act, the source ProvenanceEventCurrentState Act code would be "obsoleted".
 */
export const V3ProvenanceEventCurrentStateAS = {
  /**
   * The Act has been terminated prior to the originally intended completion.
   */
  Aborted: v3_ActStatus_Aborted,
  /**
   * The Act has been abandoned before activation.
   */
  Cancelled: v3_ActStatus_Cancelled,
  /**
   * An Act that has terminated normally after all of its constituents have been performed.
   */
  Completed: v3_ActStatus_Completed,
  /**
   * An Act that is in the preparatory stages and may not yet be acted upon
   */
  New: v3_ActStatus_New,
  /**
   * This Act instance was created in error and has been 'removed' and is treated as though it never existed.  A record is retained for audit purposes only.
   */
  Nullified: v3_ActStatus_Nullified,
  /**
   * This Act instance has been replaced by a new instance.
   */
  Obsolete: v3_ActStatus_Obsolete,
};
/**
 *  Specifies the state change of a target Act using DocuymentCompletion codes, from its previous state as a predecessor Act. For example, if the target Act is the result of a predecessor Act being "obsoleted" and replaced with the target Act, the source ProvenanceEventCurrentState Act code would be "obsoleted".
 */
export const V3ProvenanceEventCurrentStateDC = {
  /**
   * A completion status in which a document has been signed manually or electronically by one or more individuals who attest to its accuracy.  No explicit determination is made that the assigned individual has performed the authentication.  While the standard allows multiple instances of authentication, it would be typical to have a single instance of authentication, usually by the assigned individual.
   */
  Authenticated: v3_DocumentCompletion_Authenticated,
  /**
   * A completion status in which information has been orally recorded but not yet transcribed.
   */
  Dictated: v3_DocumentCompletion_Dictated,
  /**
   * A completion status in which document content, other than dictation, has been received but has not been translated into the final electronic format.  Examples include paper documents, whether hand-written or typewritten, and intermediate electronic forms, such as voice to text.
   */
  Documented: v3_DocumentCompletion_Documented,
  /**
   * A completion status in which a document has been signed manually or electronically by the individual who is legally responsible for that document. This is the most mature state in the workflow progression.
   */
  LegallyAuthenticated: v3_DocumentCompletion_LegallyAuthenticated,
  /**
   * A completion status where the document is complete and there is no expectation that the document will be signed.
   */
  UnsignedCompletedDocument: v3_DocumentCompletion_UnsignedCompletedDocument,
};
/**
 *  Supports communication of purpose of use at a general level.
 */
export const V3PurposeOfUse = {
  /**
   * To perform one or more operations on information for conducting scientific investigations to obtain health care knowledge. Use of the data must be related to specified biomedical basic or applied research.  For example, research on rare plants to determine whether biologic properties may be useful for pharmaceutical development. May be used in combination with clinical trial and other healthcare research purposes of use.
   */
  BiomedicalResearch: v3_ActReason_BiomedicalResearch,
  /**
   * To perform policy override operations on information for provision of immediately needed health care for an emergent condition affecting potential harm, death or patient safety by end users who are not provisioned for this purpose of use.  Includes override of organizational provisioning policies and may include override of subject of care consent directive restricting access.
   *
   *
   *                            Map: Partially Maps to ISO 14265 Classification Term "Emergency care provision to an individual subject of care" described as "To inform persons needing to provide health care services to the subject of care urgently, possibly needing to over-ride the  policies and consents pertaining to Purpose 1 above." Purpose 1 is equivalent to HL7 treatment purpose of use: "Clinical care provision to an individual subject of care" described as "To inform persons or processes responsible for providing health care services to the subject of care."
   * The ISO description conflates both of the proposed specializations of HL7 ETREAT: break the glass and the typically broader access to health information normally available to providers who are provisioned for emergency workflows on a regular basis, e.g., Emergency Room providers. Examples of greater access than is normally accessible by providers based on the need to know are access to sensitive information for which access typically requires a patient's consent.  This is not an override of a patient's dissent to disclose sensitive information in cases where the applicable policy waives the need for that consent to access this information. In US, Title 38 Section 7332 and 42 CFR Part 2 both permit emergency access without the need to override a patient's consent directive; rather, this access is a limitation to the patient's right to dissent from disclosure.
   */
  BreakTheGlass: v3_ActReason_BreakTheGlass,
  /**
   * To perform analytics, evaluation and other secondary uses of treatment and healthcare related information to manage the quality, efficacy, patient safety, population health, and cost effectiveness of healthcare delivery. Explicitly excludes the use of information to organize the delivery of health care for care coordination and case management, or to provide healthcare treatment.
   *
   *
   *                            Usage Note: The concept of care management is narrower than the list of activities related to more general organizational objectives such as provider profiling, education of healthcare and non-healthcare professionals; insurance underwriting, premium rating, reinsurance; organizational legal, medical review, auditing, compliance and fraud and abuse detection; business planning, development, and restructuring; fund-raising; and customer service.
   *
   *
   *                            Map: Maps to ISO 14265 Classification Term "Health service management and quality assurance" described as "To inform persons or processes responsible for determining the availability, quality, safety, equity and cost-effectiveness of health care services."
   *
   *                         There is a semantic gap in concepts.  This classification term  is described as activities, i.e., "to inform persons" or "to inform processes" rather than the rationale for performing actions/operations on information related to the activity.
   */
  CareManagement: v3_ActReason_CareManagement,
  /**
   * To perform one or more operations on information for conducting scientific investigations in accordance with clinical trial protocols to obtain health care knowledge.
   */
  ClinicalTrialResearch: v3_ActReason_ClinicalTrialResearch,
  /**
   * To perform one or more operations on information for conducting scientific investigations in accordance with clinical trial protocols to obtain health care knowledge without provision of patient care. May be post-coordinated or used with other purposes of use such as disease, discipline, specialty, population origins or ancestry, translational healthcare research. For example, a clinical trial conducted on laboratory specimens collected from a specified patient population.
   */
  ClinicalTrialResearchWithoutPatientCare: v3_ActReason_ClinicalTrialResearchWithoutPatientCare,
  /**
   * To perform one or more operations on information for conducting scientific investigations with patient care in accordance with clinical trial protocols to obtain health care knowledge. May be post-coordinated or used with other purposes of use such as disease, discipline, specialty, population origins or ancestry, translational healthcare research. For example, an "off-label" drug used for cancer therapy administer to a specified patient population.
   */
  ClinicalTrialResearchWithPatientCare: v3_ActReason_ClinicalTrialResearchWithPatientCare,
  /**
   * To perform health care as part of the clinical trial protocol.
   */
  ClinicalTrial: v3_ActReason_ClinicalTrial,
  /**
   * To perform one or more operations on information for provision of additional clinical evidence in support of a request for coverage or payment for health services.
   */
  ClaimAttachment: v3_ActReason_ClaimAttachment,
  /**
   * To perform one or more actions on information in order to organize the provision and case management of an individualâ€™s healthcare, including: Monitoring a person's goals, needs, and preferences; acting as the communication link between two or more participants concerned with a person's health and wellness; organizing and facilitating care activities and promoting self-management by advocating for, empowering, and educating a person; and ensuring safe, appropriate, non-duplicative, and effective integrated care.
   *
   *
   *                            Usage Note: Use when describing these functions: 1. Monitoring a personâ€™s goals, needs, and preferences.   2. Acting as the communication link between two or more participants concerned with a person's health and wellness.  3. Organizing and facilitating care activities and promoting self-management by advocating for, empowering, and educating a person.  4. Ensuring safe, appropriate, non-duplicative, and effective integrated care.
   *
   *                         The goal is to clearly differentiate this type of coordination of care from HIPAA Operations by specifying that these actions on information are undertaken in the provision of healthcare treatment.
   *
   *                         For similar uses of this concept, see SAMHSA Confidentiality of Substance Use Disorder Patient Records Supplemental notice of proposed rulemaking, which differentiates concepts of care coordination and case management for the provision of treatment as specifically distinct from activities related to health care delivery management and the operations of organizational entities involved in the delivery of healthcare.
   *
   *
   *                            Map: Maps to ISO 14265 Classification Terms: "Support of care activities within the provider organisation for an individual subject of care" described as "To inform persons or processes enabling others to provide health care services to the subject of care."  "Subject of Care Uses" described as "To inform the subject of care in support of his or her own interests."
   */
  CoordinationOfCare: v3_ActReason_CoordinationOfCare,
  /**
   * To perform one or more operations on information for conducting prior authorization or predetermination of coverage for services.
   */
  CoverageAuthorization: v3_ActReason_CoverageAuthorization,
  /**
   * To perform one or more operations on information for conducting activities related to coverage under a program or policy.
   */
  CoverageUnderPolicyOrProgram: v3_ActReason_CoverageUnderPolicyOrProgram,
  /**
   * To perform one or more operations on information used for provision of immediately needed health care to a population of living subjects located in a disaster zone.
   */
  Disaster: v3_ActReason_Disaster,
  /**
   * To perform one or more operations on information used for cadaveric organ, eye or tissue donation.
   */
  Donation: v3_ActReason_Donation,
  /**
   * To perform one or more operations on information for conducting scientific investigations to obtain health care knowledge. Use of the data must be related to specified conditions, diagnosis, or disease healthcare research.  For example, conducting cancer research by testing reaction of tumor cells to certain biologics. May be used in combination with clinical trial and other healthcare research purposes of use.
   */
  DiseaseSpecificHealthcareResearch: v3_ActReason_DiseaseSpecificHealthcareResearch,
  /**
   * To perform one or more operations on information used for conducting eligibility determination for coverage in a program or policy.  May entail review of financial status or disability assessment.
   */
  EligibilityDetermination: v3_ActReason_EligibilityDetermination,
  /**
   * To perform one or more operations on information used for conducting eligibility verification of coverage in a program or policy.  May entail provider contacting coverage source (e.g., government health program such as workers compensation or health plan) for confirmation of enrollment, eligibility for specific services, and any applicable copays.
   */
  EligibilityVerification: v3_ActReason_EligibilityVerification,
  /**
   * To perform one or more operations on information used for enrolling a covered party in a program or policy.  May entail recording of covered party's and any dependent's demographic information and benefit choices.
   */
  Enrollment: v3_ActReason_Enrollment,
  /**
   * To perform one or more operations on information for provision of immediately needed health care for an emergent condition in an emergency room or similar emergent care context by end users provisioned for this purpose, which does not constitute as policy override such as in a "Break the Glass" purpose of use.
   *
   *                         Map:Partially Maps to ISO 14265 Classification Term "Emergency care provision to an individual subject of care" described as "To inform persons needing to provide health care services to the subject of care urgently, possibly needing to over-ride the  policies and consents pertaining to Purpose 1 above." Purpose 1 is equivalent to HL7 treatment purpose of use: "Clinical care provision to an individual subject of care" described as "To inform persons or processes responsible for providing health care services to the subject of care."
   *
   *                         The ISO description conflates both of the proposed specializations of HL7 ETREAT: break the glass and the typically broader access to health information normally available to providers who are provisioned for emergency workflows on a regular basis, e.g., Emergency Room providers. Examples of greater access than is normally accessible by providers based on the need to know are access to sensitive information for which access typically requires a patient's consent.  This is not an override of a patient's dissent to disclose sensitive information in cases where the applicable policy waives the need for that consent to access this information. In US, Title 38 Section 7332 and 42 CFR Part 2 both permit emergency access without the need to override a patient's consent directive; rather, this access is a limitation to the patient's right to dissent from disclosure.
   *
   *                         There is a semantic gap in concepts.  This classification term is described as activities â€œto inform personsâ€? rather than the rationale for performing actions/operations on information related to the activity.
   */
  EmergencyRoomTreatment: v3_ActReason_EmergencyRoomTreatment,
  /**
   * To perform one or more operations on information for provision of immediately needed health care for an emergent condition.
   */
  EmergencyTreatment: v3_ActReason_EmergencyTreatment,
  /**
   * To perform one or more operations on information in response to a request by a family member authorized by the patient.
   */
  FamilyRequested: v3_ActReason_FamilyRequested,
  /**
   * To perform one or more operations on information used for fraud detection and prevention processes.
   */
  Fraud: v3_ActReason_Fraud,
  /**
   * To perform one or more operations on information used within government processes.
   */
  Government: v3_ActReason_Government,
  /**
   * To perform one or more operations on information for conducting activities related to meeting accreditation criteria.
   */
  HealthAccreditation: v3_ActReason_HealthAccreditation,
  /**
   * To perform one or more operations on information used for conducting activities required to meet a mandate.
   */
  HealthCompliance: v3_ActReason_HealthCompliance,
  /**
   * To perform one or more operations on information used for handling deceased patient matters.
   */
  Decedent: v3_ActReason_Decedent,
  /**
   * To perform one or more operation operations on information used to manage a patient directory.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            facility
   *                            enterprise
   *                            payer
   *                            health information exchange patient directory
   */
  Directory: v3_ActReason_Directory,
  /**
   * To perform one or more actions on information used for conducting administrative and contractual activities by or on behalf of organizational entities responsible for delivery of  an individual's benefits in a healthcare program, health plan or insurance.   Explicitly excludes the use of information to organize the delivery of health care for care coordination and case management, or to provide healthcare treatment.
   *
   *
   *                            Usage Note: Examples of activities conducted under this purpose of use: provider profiling, risk adjustment, underwriting, fraud and abuse, quality improvement population health and care management. Aligns with HIPAA Operation POU minus coordination of care or other treatment related activities. Similar to the description in SAMHSA Confidentiality of Substance Use Disorder Patient Records Supplemental notice of proposed rulemaking.
   *
   *
   *                            Map: Maps to ISO 14265 Classification Term  "Administration of care for an individual subject of care" described as "To inform persons or processes responsible for enabling the availability of resources or funding or permissions for providing health care services to the subject of care."
   *
   *                         However, this classification term is described as activities, i.e., "to inform persons" or "to inform processes" rather than the rationale for performing actions/operations on information related to the activity.
   */
  HealthcareDeliveryManagement: v3_ActReason_HealthcareDeliveryManagement,
  /**
   * To perform one or more operations on information for conducting activities required by legal proceeding.
   */
  Legal: v3_ActReason_Legal,
  /**
   * To perform one or more operations on information for marketing services and products related to health care.
   */
  HealthcareMarketing: v3_ActReason_HealthcareMarketing,
  /**
   * To perform one or more operations on information used for conducting administrative and contractual activities related to the provision of health care.
   */
  HealthcareOperations: v3_ActReason_HealthcareOperations,
  /**
   * To perform one or more operations on information used for assessing results and comparative effectiveness achieved by health care practices and interventions.
   */
  HealthOutcomeMeasure: v3_ActReason_HealthOutcomeMeasure,
  /**
   * To perform one or more operations on information for conducting financial or contractual activities related to payment for provision of health care.
   */
  HealthcarePayment: v3_ActReason_HealthcarePayment,
  /**
   * To perform one or more operations on information used for conducting activities to meet program accounting requirements.
   */
  HealthProgramReporting: v3_ActReason_HealthProgramReporting,
  /**
   * To perform one or more operations on information used for conducting administrative activities to improve health care quality.
   */
  HealthQualityImprovement: v3_ActReason_HealthQualityImprovement,
  /**
   * To perform one or more operations on information for conducting scientific investigations to obtain health care knowledge.  Use of the data iincludes basic and applied research such as biomedical, population origin or ancestry, translational research, and disease, discipline, specialty specific healthcare research and clinical trial research.
   */
  HealthcareResearch: v3_ActReason_HealthcareResearch,
  /**
   * To perform one or more operations on information to administer the electronic systems used for the delivery of health care.
   */
  HealthSystemAdministration: v3_ActReason_HealthSystemAdministration,
  /**
   * To perform one or more operations on information that is simulated or synthetic health data used for testing system capabilities outside of a production or operational system environment.
   *
   *
   *                            Usage Note: Data marked with a HTEST security label enables an access control system to permit interfacing systems or end users provisioned with a clearance, which includes a HTEST purpose of use attribute, to test, verify, or validate that a system or application will operate in production as intended based on design specifications.
   */
  TestHealthData: v3_ActReason_TestHealthData,
  /**
   * To perform one or more operations on information to assign, persist, and manage labels to healthcare data to characterize various aspects, such as its security classification, sensitivity, compartment, integrity, and provenance; applicable privacy, consent, security, provenance, and trust policies; and handling caveats such as purpose of use, obligations, and refrain policies.
   *
   *                         Label management includes classification of target data by constructing and binding of a label set per applicable policies, security policy information file semantics, and classification guides.  Label management also includes process and procedures for subsequent revision of a label for, e.g., reclassification, downgrading classification, and declassification.
   *
   *                         Label revisions may be triggered by, e.g., expiry of classification period; changes in applicable policy, e.g., revocation of a consent directive; or changes in the governing policy domain in which the data is relocated or a copy of the data is sent.  If a label is revised, an audit log should be kept and the provenance of the label changes should be tracked.
   */
  Labeling: v3_ActReason_Labeling,
  /**
   * To perform one or more operations on information to administer health care coverage to an enrollee under a policy or program.
   */
  MemberAdministration: v3_ActReason_MemberAdministration,
  /**
   * To perform one or more operations on information to assign, persist, and manage metadata to healthcare data to characterize various aspects used for its indexing, discovery, retrieval, and processing by systems, applications, and end users.  For example, master index identifier, media type, and location.
   */
  MetadataManagement: v3_ActReason_MetadataManagement,
  /**
   * To perform one or more operations on information for conducting activities required by military processes, procedures, policies, or law.
   */
  MilitaryCommand: v3_ActReason_MilitaryCommand,
  /**
   * To perform one or more operations on information for the process of releasing military personnel from their service obligations, which may include determining service merit, discharge benefits, and disability assessment.
   */
  MilitaryDischarge: v3_ActReason_MilitaryDischarge,
  /**
   * To perform one or more operations on information used for operational activities conducted to administer the delivery of health care to a patient.
   */
  PatientAdministration: v3_ActReason_PatientAdministration,
  /**
   * To perform one or more operations on information in response to a patient's request.
   */
  PatientRequested: v3_ActReason_PatientRequested,
  /**
   * To perform one or more operations on information in processes related to ensuring the safety of health care.
   */
  PatientSafety: v3_ActReason_PatientSafety,
  /**
   * To perform one or more operations on information used for monitoring performance of recommended health care practices and interventions.
   */
  PerformanceMeasure: v3_ActReason_PerformanceMeasure,
  /**
   * To perform one or more operations on information, including genealogical pedigrees, historical records, surveys, family health data, health records, and genetic information, for conducting scientific investigations to obtain health care knowledge. Use of the data must be related to population origins and/or ancestry healthcare research.  For example, gathering genetic specimens from a specific population in order to determine the ancestry and population origins of that group. May be used in combination with clinical trial and other healthcare research purposes of use.
   */
  PopulationOriginsOrAncestryHealthcareResearch: v3_ActReason_PopulationOriginsOrAncestryHealthcareResearch,
  /**
   * To perform one or more operations on information for provision of health care to a population of living subjects, e.g., needle exchange program.
   */
  PopulationHealth: v3_ActReason_PopulationHealth,
  /**
   * To perform one or more operations on information in preparation for conducting scientific investigation to obtain health care knowledge, such as research on animals or review of patient health records, to determine the feasibility of a clinical trial study; assist with protocol design; or in preparation for institutional review board or ethics committee approval process.  May be post-coordinated or used with other purposes of use such as disease, discipline, specialty, population origins or ancestry, translational healthcare research.
   */
  PreclinicalTrialResearch: v3_ActReason_PreclinicalTrialResearch,
  /**
   * To perform one or more operations on information for conducting public health activities, such as the reporting of notifiable conditions.
   */
  PublicHealth: v3_ActReason_PublicHealth,
  /**
   * Reason for performing one or more operations on information, which may be permitted by source system's security policy in accordance with one or more privacy policies and consent directives.
   *
   *
   *                            Usage Notes: The rationale or purpose for an act relating to the management of personal health information, such as collecting personal health information for research or public health purposes.
   */
  PurposeOfUse: v3_ActReason_PurposeOfUse,
  /**
   * To perform one or more operations on information in response to a request by a person appointed as the patient's legal representative.
   */
  PowerOfAttorney: v3_ActReason_PowerOfAttorney,
  /**
   * To perform one or more operations on information used within the health records management process.
   */
  RecordsManagement: v3_ActReason_RecordsManagement,
  /**
   * To perform one or more operations on information about the amount remitted for a health care claim.
   */
  RemittanceAdvice: v3_ActReason_RemittanceAdvice,
  /**
   * To perform one or more operations on information in response to a request by a person authorized by the patient.
   */
  SupportNetwork: v3_ActReason_SupportNetwork,
  /**
   * To perform one or more operations on information to design, develop, implement, test, or deploy a healthcare system or application.
   */
  SystemDevelopment: v3_ActReason_SystemDevelopment,
  /**
   * To perform one or more operations on information used to prevent injury or disease to living subjects who may be the target of violence.
   */
  Threat: v3_ActReason_Threat,
  /**
   * To perform one or more operations on information used in training and education.
   */
  Training: v3_ActReason_Training,
  /**
   * To perform one or more operations on information for conducting scientific investigations to obtain health care knowledge related to evidence based medicine during the course of providing healthcare treatment.  Sometimes referred to as "bench to bedside", which is the iterative feedback loop between healthcare research and clinical trials with input from information collected in the course of routine provision of healthcare. For example, by extending a patient encounter to conduct a survey related to a research topic such as attitudes about use of a wellness device that a patient agreed to use. May be used in combination with clinical trial and other healthcare research purposes of use.
   */
  TranslationalHealthcareResearch: v3_ActReason_TranslationalHealthcareResearch,
  /**
   * To perform one or more operations on information for provision of health care.
   */
  Treatment: v3_ActReason_Treatment,
};
const v3_QueryParameterValue_DispenseQueryFilterCode: Coding = {
  code: '_DispenseQueryFilterCode',
  display: 'dispense query filter code',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_OrderFilterCode: Coding = {
  code: '_OrderFilterCode',
  display: '_OrderFilterCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_PrescriptionDispenseFilterCode: Coding = {
  code: '_PrescriptionDispenseFilterCode',
  display: 'Prescription Dispense Filter Code',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_QueryParameterValue: Coding = {
  code: '_QueryParameterValue',
  display: 'QueryParameterValue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_AllDispenses: Coding = {
  code: 'ALLDISP',
  display: 'all dispenses',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_AllOrders: Coding = {
  code: 'AO',
  display: 'all orders',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_CompletelyDispensed: Coding = {
  code: 'C',
  display: 'Completely dispensed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_All: Coding = {
  code: 'ISSFA',
  display: 'all',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_WithIssues: Coding = {
  code: 'ISSFI',
  display: 'with issues',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_WithUnmanagedIssues: Coding = {
  code: 'ISSFU',
  display: 'with unmanaged issues',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_LastDispense: Coding = {
  code: 'LASTDISP',
  display: 'last dispense',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_NeverDispensed: Coding = {
  code: 'N',
  display: 'Never Dispensed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_NoDispense: Coding = {
  code: 'NODISP',
  display: 'no dispense',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_OrdersWithoutResults: Coding = {
  code: 'ONR',
  display: 'orders without results',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_OrdersWithResults: Coding = {
  code: 'OWR',
  display: 'orders with results',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
const v3_QueryParameterValue_DispensedWithRemainingFills: Coding = {
  code: 'R',
  display: 'Dispensed with remaining fills',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryParameterValue',
};
/**
 *  The domain of coded values used as parameters within QueryByParameter queries.
 */
export const V3QueryParameterValue = {
  /**
   * Description:Filter codes used to manage volume of dispenses returned by  a parameter-based queries.
   */
  DispenseQueryFilterCode: v3_QueryParameterValue_DispenseQueryFilterCode,
  /**
   * Filter codes used to manage types of orders being returned by a parameter-based query.
   */
  OrderFilterCode: v3_QueryParameterValue_OrderFilterCode,
  /**
   * A "helper" vocabulary used to construct complex query filters based on how and whether a prescription has been dispensed.
   */
  PrescriptionDispenseFilterCode: v3_QueryParameterValue_PrescriptionDispenseFilterCode,
  /**
   * Description:Indicates how result sets should be filtered based on whether they have associated issues.
   */
  QueryParameterValue: v3_QueryParameterValue_QueryParameterValue,
  /**
   * Description:Returns all dispenses to date for a prescription.
   */
  AllDispenses: v3_QueryParameterValue_AllDispenses,
  /**
   * Return all orders.
   */
  AllOrders: v3_QueryParameterValue_AllOrders,
  /**
   * Filter to only include SubstanceAdministration orders which have no remaining quantity authorized to be dispensed.
   */
  CompletelyDispensed: v3_QueryParameterValue_CompletelyDispensed,
  /**
   * Description:Result set should not be filtered based on the presence of issues.
   */
  All: v3_QueryParameterValue_All,
  /**
   * Description:Result set should be filtered to only include records with associated issues.
   */
  WithIssues: v3_QueryParameterValue_WithIssues,
  /**
   * Description:Result set should be filtered to only include records with associated unmanaged issues.
   */
  WithUnmanagedIssues: v3_QueryParameterValue_WithUnmanagedIssues,
  /**
   * Description:Returns the most recent dispense for a prescription.
   */
  LastDispense: v3_QueryParameterValue_LastDispense,
  /**
   * Filter to only include SubstanceAdministration orders which have no fulfilling supply events performed.
   */
  NeverDispensed: v3_QueryParameterValue_NeverDispensed,
  /**
   * Description:Returns no dispense for a prescription.
   */
  NoDispense: v3_QueryParameterValue_NoDispense,
  /**
   * Return only those orders that do not have results.
   */
  OrdersWithoutResults: v3_QueryParameterValue_OrdersWithoutResults,
  /**
   * Return only those orders that have results.
   */
  OrdersWithResults: v3_QueryParameterValue_OrdersWithResults,
  /**
   * Filter to only include SubstanceAdministration orders which have had at least one fulfilling supply event, but which still have outstanding quantity remaining to be authorized.
   */
  DispensedWithRemainingFills: v3_QueryParameterValue_DispensedWithRemainingFills,
};
const v3_QueryPriority_Deferred: Coding = {
  code: 'D',
  display: 'Deferred',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryPriority',
};
const v3_QueryPriority_Immediate: Coding = {
  code: 'I',
  display: 'Immediate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryPriority',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3QueryPriority = {
  /**
   * Query response is deferred.
   */
  Deferred: v3_QueryPriority_Deferred,
  /**
   * Query response is immediate.
   */
  Immediate: v3_QueryPriority_Immediate,
};
const v3_QueryRequestLimit_QueryRequestLimit: Coding = {
  code: '_QueryRequestLimit',
  display: 'QueryRequestLimit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryRequestLimit',
};
const v3_QueryRequestLimit_Record: Coding = {
  code: 'RD',
  display: 'record',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryRequestLimit',
};
/**
 *   Definition:
 * Defines the units associated with the magnitude of the maximum size limit of a query response that can be accepted by the requesting application.
 */
export const V3QueryRequestLimit = {
  /**
   * Definition: The number of matching instances (number of focal classes). The document header class is the focal class of a document, a record would therefore be equal to a document.
   */
  QueryRequestLimit: v3_QueryRequestLimit_QueryRequestLimit,
  /**
   * Definition: The number of matching instances (number of focal classes). The document header class is the focal class of a document, a record would therefore be equal to a document.
   */
  Record: v3_QueryRequestLimit_Record,
};
const v3_QueryResponse_ApplicationError: Coding = {
  code: 'AE',
  display: 'ApplicationError',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryResponse',
};
const v3_QueryResponse_NoDataFound: Coding = {
  code: 'NF',
  display: 'No data found',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryResponse',
};
const v3_QueryResponse_DataFound: Coding = {
  code: 'OK',
  display: 'Data found',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryResponse',
};
const v3_QueryResponse_QueryParameterError: Coding = {
  code: 'QE',
  display: 'QueryParameterError',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryResponse',
};
/**
 *  A code classifying the general nature of the response to a given query. Includes whether or not data was found, or whether an error occurred.
 */
export const V3QueryResponse = {
  /**
   * Query Error.  Application Error.
   */
  ApplicationError: v3_QueryResponse_ApplicationError,
  /**
   * No errors, but no data was found matching the query request specification.
   */
  NoDataFound: v3_QueryResponse_NoDataFound,
  /**
   * Query reponse data found for 1 or more result sets matching the query request specification.
   */
  DataFound: v3_QueryResponse_DataFound,
  /**
   * QueryError. Problem with input ParmetersError
   */
  QueryParameterError: v3_QueryResponse_QueryParameterError,
};
const v3_QueryStatusCode_Aborted: Coding = {
  code: 'aborted',
  display: 'aborted',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryStatusCode',
};
const v3_QueryStatusCode_DeliveredResponse: Coding = {
  code: 'deliveredResponse',
  display: 'deliveredResponse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryStatusCode',
};
const v3_QueryStatusCode_Executing: Coding = {
  code: 'executing',
  display: 'executing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryStatusCode',
};
const v3_QueryStatusCode_New: Coding = {
  code: 'new',
  display: 'new',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryStatusCode',
};
const v3_QueryStatusCode_WaitContinuedQueryResponse: Coding = {
  code: 'waitContinuedQueryResponse',
  display: 'waitContinuedQueryResponse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-QueryStatusCode',
};
/**
 *  A code specifying the state of the Query.
 */
export const V3QueryStatusCode = {
  /**
   * Query status aborted
   */
  Aborted: v3_QueryStatusCode_Aborted,
  /**
   * Query Status delivered response
   */
  DeliveredResponse: v3_QueryStatusCode_DeliveredResponse,
  /**
   * Query Status executing
   */
  Executing: v3_QueryStatusCode_Executing,
  /**
   * Query Status new
   */
  New: v3_QueryStatusCode_New,
  /**
   * Query Status wait continued
   */
  WaitContinuedQueryResponse: v3_QueryStatusCode_WaitContinuedQueryResponse,
};
const v3_Race_AmericanIndian: Coding = {
  code: '1004-1',
  display: 'American Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Abenaki: Coding = {
  code: '1006-6',
  display: 'Abenaki',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Algonquian: Coding = {
  code: '1008-2',
  display: 'Algonquian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Apache: Coding = {
  code: '1010-8',
  display: 'Apache',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chiricahua: Coding = {
  code: '1011-6',
  display: 'Chiricahua',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FortSillApache: Coding = {
  code: '1012-4',
  display: 'Fort Sill Apache',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_JicarillaApache: Coding = {
  code: '1013-2',
  display: 'Jicarilla Apache',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LipanApache: Coding = {
  code: '1014-0',
  display: 'Lipan Apache',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MescaleroApache: Coding = {
  code: '1015-7',
  display: 'Mescalero Apache',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaApache: Coding = {
  code: '1016-5',
  display: 'Oklahoma Apache',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PaysonApache: Coding = {
  code: '1017-3',
  display: 'Payson Apache',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SanCarlosApache: Coding = {
  code: '1018-1',
  display: 'San Carlos Apache',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WhiteMountainApache: Coding = {
  code: '1019-9',
  display: 'White Mountain Apache',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Arapaho: Coding = {
  code: '1021-5',
  display: 'Arapaho',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NorthernArapaho: Coding = {
  code: '1022-3',
  display: 'Northern Arapaho',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SouthernArapaho: Coding = {
  code: '1023-1',
  display: 'Southern Arapaho',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WindRiverArapaho: Coding = {
  code: '1024-9',
  display: 'Wind River Arapaho',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Arikara: Coding = {
  code: '1026-4',
  display: 'Arikara',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Assiniboine: Coding = {
  code: '1028-0',
  display: 'Assiniboine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AssiniboineSioux: Coding = {
  code: '1030-6',
  display: 'Assiniboine Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FortPeckAssiniboineSioux: Coding = {
  code: '1031-4',
  display: 'Fort Peck Assiniboine Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Bannock: Coding = {
  code: '1033-0',
  display: 'Bannock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Blackfeet: Coding = {
  code: '1035-5',
  display: 'Blackfeet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Brotherton: Coding = {
  code: '1037-1',
  display: 'Brotherton',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BurtLakeBand: Coding = {
  code: '1039-7',
  display: 'Burt Lake Band',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Caddo: Coding = {
  code: '1041-3',
  display: 'Caddo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaCado: Coding = {
  code: '1042-1',
  display: 'Oklahoma Cado',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cahuilla: Coding = {
  code: '1044-7',
  display: 'Cahuilla',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AguaCalienteCahuilla: Coding = {
  code: '1045-4',
  display: 'Agua Caliente Cahuilla',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Augustine: Coding = {
  code: '1046-2',
  display: 'Augustine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cabazon: Coding = {
  code: '1047-0',
  display: 'Cabazon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LosCoyotes: Coding = {
  code: '1048-8',
  display: 'Los Coyotes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Morongo: Coding = {
  code: '1049-6',
  display: 'Morongo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SantaRosaCahuilla: Coding = {
  code: '1050-4',
  display: 'Santa Rosa Cahuilla',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TorresMartinez: Coding = {
  code: '1051-2',
  display: 'Torres-Martinez',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CaliforniaTribes: Coding = {
  code: '1053-8',
  display: 'California Tribes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cahto: Coding = {
  code: '1054-6',
  display: 'Cahto',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chimariko: Coding = {
  code: '1055-3',
  display: 'Chimariko',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CoastMiwok: Coding = {
  code: '1056-1',
  display: 'Coast Miwok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Digger: Coding = {
  code: '1057-9',
  display: 'Digger',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kawaiisu: Coding = {
  code: '1058-7',
  display: 'Kawaiisu',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_KernRiver: Coding = {
  code: '1059-5',
  display: 'Kern River',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Mattole: Coding = {
  code: '1060-3',
  display: 'Mattole',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_RedWood: Coding = {
  code: '1061-1',
  display: 'Red Wood',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SantaRosa: Coding = {
  code: '1062-9',
  display: 'Santa Rosa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Takelma: Coding = {
  code: '1063-7',
  display: 'Takelma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wappo: Coding = {
  code: '1064-5',
  display: 'Wappo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Yana: Coding = {
  code: '1065-2',
  display: 'Yana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Yuki: Coding = {
  code: '1066-0',
  display: 'Yuki',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CanadianAndLatinAmericanIndian: Coding = {
  code: '1068-6',
  display: 'Canadian and Latin American Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CanadianIndian: Coding = {
  code: '1069-4',
  display: 'Canadian Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CentralAmericanIndian: Coding = {
  code: '1070-2',
  display: 'Central American Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FrenchAmericanIndian: Coding = {
  code: '1071-0',
  display: 'French American Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MexicanAmericanIndian: Coding = {
  code: '1072-8',
  display: 'Mexican American Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SouthAmericanIndian: Coding = {
  code: '1073-6',
  display: 'South American Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SpanishAmericanIndian: Coding = {
  code: '1074-4',
  display: 'Spanish American Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Catawba: Coding = {
  code: '1076-9',
  display: 'Catawba',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cayuse: Coding = {
  code: '1078-5',
  display: 'Cayuse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chehalis: Coding = {
  code: '1080-1',
  display: 'Chehalis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chemakuan: Coding = {
  code: '1082-7',
  display: 'Chemakuan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Hoh: Coding = {
  code: '1083-5',
  display: 'Hoh',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Quileute: Coding = {
  code: '1084-3',
  display: 'Quileute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chemehuevi: Coding = {
  code: '1086-8',
  display: 'Chemehuevi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cherokee: Coding = {
  code: '1088-4',
  display: 'Cherokee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CherokeeAlabama: Coding = {
  code: '1089-2',
  display: 'Cherokee Alabama',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CherokeesOfNortheastAlabama: Coding = {
  code: '1090-0',
  display: 'Cherokees of Northeast Alabama',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CherokeesOfSoutheastAlabama: Coding = {
  code: '1091-8',
  display: 'Cherokees of Southeast Alabama',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_EasternCherokee: Coding = {
  code: '1092-6',
  display: 'Eastern Cherokee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_EchotaCherokee: Coding = {
  code: '1093-4',
  display: 'Echota Cherokee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_EtowahCherokee: Coding = {
  code: '1094-2',
  display: 'Etowah Cherokee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NorthernCherokee: Coding = {
  code: '1095-9',
  display: 'Northern Cherokee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tuscola: Coding = {
  code: '1096-7',
  display: 'Tuscola',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_UnitedKeetowahBandOfCherokee: Coding = {
  code: '1097-5',
  display: 'United Keetowah Band of Cherokee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WesternCherokee: Coding = {
  code: '1098-3',
  display: 'Western Cherokee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CherokeeShawnee: Coding = {
  code: '1100-7',
  display: 'Cherokee Shawnee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cheyenne: Coding = {
  code: '1102-3',
  display: 'Cheyenne',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NorthernCheyenne: Coding = {
  code: '1103-1',
  display: 'Northern Cheyenne',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SouthernCheyenne: Coding = {
  code: '1104-9',
  display: 'Southern Cheyenne',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CheyenneArapaho: Coding = {
  code: '1106-4',
  display: 'Cheyenne-Arapaho',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chickahominy: Coding = {
  code: '1108-0',
  display: 'Chickahominy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_EasternChickahominy: Coding = {
  code: '1109-8',
  display: 'Eastern Chickahominy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WesternChickahominy: Coding = {
  code: '1110-6',
  display: 'Western Chickahominy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chickasaw: Coding = {
  code: '1112-2',
  display: 'Chickasaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chinook: Coding = {
  code: '1114-8',
  display: 'Chinook',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Clatsop: Coding = {
  code: '1115-5',
  display: 'Clatsop',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ColumbiaRiverChinook: Coding = {
  code: '1116-3',
  display: 'Columbia River Chinook',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kathlamet: Coding = {
  code: '1117-1',
  display: 'Kathlamet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_UpperChinook: Coding = {
  code: '1118-9',
  display: 'Upper Chinook',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WakiakumChinook: Coding = {
  code: '1119-7',
  display: 'Wakiakum Chinook',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WillapaChinook: Coding = {
  code: '1120-5',
  display: 'Willapa Chinook',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wishram: Coding = {
  code: '1121-3',
  display: 'Wishram',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chippewa: Coding = {
  code: '1123-9',
  display: 'Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BadRiver: Coding = {
  code: '1124-7',
  display: 'Bad River',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BayMillsChippewa: Coding = {
  code: '1125-4',
  display: 'Bay Mills Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BoisForte: Coding = {
  code: '1126-2',
  display: 'Bois Forte',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BurtLakeChippewa: Coding = {
  code: '1127-0',
  display: 'Burt Lake Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FondDuLac: Coding = {
  code: '1128-8',
  display: 'Fond du Lac',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_GrandPortage: Coding = {
  code: '1129-6',
  display: 'Grand Portage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_GrandTraverseBandOfOttawaChippewa: Coding = {
  code: '1130-4',
  display: 'Grand Traverse Band of Ottawa-Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Keweenaw: Coding = {
  code: '1131-2',
  display: 'Keweenaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LacCourteOreilles: Coding = {
  code: '1132-0',
  display: 'Lac Courte Oreilles',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LacDuFlambeau: Coding = {
  code: '1133-8',
  display: 'Lac du Flambeau',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LacVieuxDesertChippewa: Coding = {
  code: '1134-6',
  display: 'Lac Vieux Desert Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LakeSuperior: Coding = {
  code: '1135-3',
  display: 'Lake Superior',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LeechLake: Coding = {
  code: '1136-1',
  display: 'Leech Lake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LittleShellChippewa: Coding = {
  code: '1137-9',
  display: 'Little Shell Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MilleLacs: Coding = {
  code: '1138-7',
  display: 'Mille Lacs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MinnesotaChippewa: Coding = {
  code: '1139-5',
  display: 'Minnesota Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ontonagon: Coding = {
  code: '1140-3',
  display: 'Ontonagon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_RedCliffChippewa: Coding = {
  code: '1141-1',
  display: 'Red Cliff Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_RedLakeChippewa: Coding = {
  code: '1142-9',
  display: 'Red Lake Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SaginawChippewa: Coding = {
  code: '1143-7',
  display: 'Saginaw Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_StCroixChippewa: Coding = {
  code: '1144-5',
  display: 'St. Croix Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SaultSteMarieChippewa: Coding = {
  code: '1145-2',
  display: 'Sault Ste. Marie Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SokoagonChippewa: Coding = {
  code: '1146-0',
  display: 'Sokoagon Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TurtleMountain: Coding = {
  code: '1147-8',
  display: 'Turtle Mountain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WhiteEarth: Coding = {
  code: '1148-6',
  display: 'White Earth',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ChippewaCree: Coding = {
  code: '1150-2',
  display: 'Chippewa Cree',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_RockyBoyQuoteSChippewaCree: Coding = {
  code: '1151-0',
  display: "Rocky Boy's Chippewa Cree",
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chitimacha: Coding = {
  code: '1153-6',
  display: 'Chitimacha',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Choctaw: Coding = {
  code: '1155-1',
  display: 'Choctaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CliftonChoctaw: Coding = {
  code: '1156-9',
  display: 'Clifton Choctaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_JenaChoctaw: Coding = {
  code: '1157-7',
  display: 'Jena Choctaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MississippiChoctaw: Coding = {
  code: '1158-5',
  display: 'Mississippi Choctaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MowaBandOfChoctaw: Coding = {
  code: '1159-3',
  display: 'Mowa Band of Choctaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaChoctaw: Coding = {
  code: '1160-1',
  display: 'Oklahoma Choctaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chumash: Coding = {
  code: '1162-7',
  display: 'Chumash',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SantaYnez: Coding = {
  code: '1163-5',
  display: 'Santa Ynez',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ClearLake: Coding = {
  code: '1165-0',
  display: 'Clear Lake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CoeurDQuoteAlene: Coding = {
  code: '1167-6',
  display: "Coeur D'Alene",
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Coharie: Coding = {
  code: '1169-2',
  display: 'Coharie',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ColoradoRiver: Coding = {
  code: '1171-8',
  display: 'Colorado River',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Colville: Coding = {
  code: '1173-4',
  display: 'Colville',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Comanche: Coding = {
  code: '1175-9',
  display: 'Comanche',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaComanche: Coding = {
  code: '1176-7',
  display: 'Oklahoma Comanche',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CoosLowerUmpquaSiuslaw: Coding = {
  code: '1178-3',
  display: 'Coos, Lower Umpqua, Siuslaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Coos: Coding = {
  code: '1180-9',
  display: 'Coos',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Coquilles: Coding = {
  code: '1182-5',
  display: 'Coquilles',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Costanoan: Coding = {
  code: '1184-1',
  display: 'Costanoan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Coushatta: Coding = {
  code: '1186-6',
  display: 'Coushatta',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AlabamaCoushatta: Coding = {
  code: '1187-4',
  display: 'Alabama Coushatta',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cowlitz: Coding = {
  code: '1189-0',
  display: 'Cowlitz',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cree: Coding = {
  code: '1191-6',
  display: 'Cree',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Creek: Coding = {
  code: '1193-2',
  display: 'Creek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AlabamaCreek: Coding = {
  code: '1194-0',
  display: 'Alabama Creek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AlabamaQuassarte: Coding = {
  code: '1195-7',
  display: 'Alabama Quassarte',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_EasternCreek: Coding = {
  code: '1196-5',
  display: 'Eastern Creek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_EasternMuscogee: Coding = {
  code: '1197-3',
  display: 'Eastern Muscogee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kialegee: Coding = {
  code: '1198-1',
  display: 'Kialegee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LowerMuscogee: Coding = {
  code: '1199-9',
  display: 'Lower Muscogee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MachisLowerCreekIndian: Coding = {
  code: '1200-5',
  display: 'Machis Lower Creek Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PoarchBand: Coding = {
  code: '1201-3',
  display: 'Poarch Band',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PrincipalCreekIndianNation: Coding = {
  code: '1202-1',
  display: 'Principal Creek Indian Nation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_StarClanOfMuscogeeCreeks: Coding = {
  code: '1203-9',
  display: 'Star Clan of Muscogee Creeks',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Thlopthlocco: Coding = {
  code: '1204-7',
  display: 'Thlopthlocco',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tuckabachee: Coding = {
  code: '1205-4',
  display: 'Tuckabachee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Croatan: Coding = {
  code: '1207-0',
  display: 'Croatan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Crow: Coding = {
  code: '1209-6',
  display: 'Crow',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cupeno: Coding = {
  code: '1211-2',
  display: 'Cupeno',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AguaCaliente: Coding = {
  code: '1212-0',
  display: 'Agua Caliente',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Delaware: Coding = {
  code: '1214-6',
  display: 'Delaware',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_EasternDelaware: Coding = {
  code: '1215-3',
  display: 'Eastern Delaware',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LenniLenape: Coding = {
  code: '1216-1',
  display: 'Lenni-Lenape',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Munsee: Coding = {
  code: '1217-9',
  display: 'Munsee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaDelaware: Coding = {
  code: '1218-7',
  display: 'Oklahoma Delaware',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_RampoughMountain: Coding = {
  code: '1219-5',
  display: 'Rampough Mountain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SandHill: Coding = {
  code: '1220-3',
  display: 'Sand Hill',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Diegueno: Coding = {
  code: '1222-9',
  display: 'Diegueno',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Campo: Coding = {
  code: '1223-7',
  display: 'Campo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CapitanGrande: Coding = {
  code: '1224-5',
  display: 'Capitan Grande',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cuyapaipe: Coding = {
  code: '1225-2',
  display: 'Cuyapaipe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LaPosta: Coding = {
  code: '1226-0',
  display: 'La Posta',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Manzanita: Coding = {
  code: '1227-8',
  display: 'Manzanita',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MesaGrande: Coding = {
  code: '1228-6',
  display: 'Mesa Grande',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SanPasqual: Coding = {
  code: '1229-4',
  display: 'San Pasqual',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SantaYsabel: Coding = {
  code: '1230-2',
  display: 'Santa Ysabel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Sycuan: Coding = {
  code: '1231-0',
  display: 'Sycuan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_EasternTribes: Coding = {
  code: '1233-6',
  display: 'Eastern Tribes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Attacapa: Coding = {
  code: '1234-4',
  display: 'Attacapa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Biloxi: Coding = {
  code: '1235-1',
  display: 'Biloxi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Georgetown: Coding = {
  code: '1236-9',
  display: 'Georgetown',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Moor: Coding = {
  code: '1237-7',
  display: 'Moor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nansemond: Coding = {
  code: '1238-5',
  display: 'Nansemond',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Natchez: Coding = {
  code: '1239-3',
  display: 'Natchez',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NausuWaiwash: Coding = {
  code: '1240-1',
  display: 'Nausu Waiwash',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nipmuc: Coding = {
  code: '1241-9',
  display: 'Nipmuc',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Paugussett: Coding = {
  code: '1242-7',
  display: 'Paugussett',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PocomokeAcohonock: Coding = {
  code: '1243-5',
  display: 'Pocomoke Acohonock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SoutheasternIndians: Coding = {
  code: '1244-3',
  display: 'Southeastern Indians',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Susquehanock: Coding = {
  code: '1245-0',
  display: 'Susquehanock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TunicaBiloxi: Coding = {
  code: '1246-8',
  display: 'Tunica Biloxi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WaccamawSiousan: Coding = {
  code: '1247-6',
  display: 'Waccamaw-Siousan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wicomico: Coding = {
  code: '1248-4',
  display: 'Wicomico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Esselen: Coding = {
  code: '1250-0',
  display: 'Esselen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FortBelknap: Coding = {
  code: '1252-6',
  display: 'Fort Belknap',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FortBerthold: Coding = {
  code: '1254-2',
  display: 'Fort Berthold',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FortMcdowell: Coding = {
  code: '1256-7',
  display: 'Fort Mcdowell',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FortHall: Coding = {
  code: '1258-3',
  display: 'Fort Hall',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Gabrieleno: Coding = {
  code: '1260-9',
  display: 'Gabrieleno',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_GrandRonde: Coding = {
  code: '1262-5',
  display: 'Grand Ronde',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_GrosVentres: Coding = {
  code: '1264-1',
  display: 'Gros Ventres',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Atsina: Coding = {
  code: '1265-8',
  display: 'Atsina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Haliwa: Coding = {
  code: '1267-4',
  display: 'Haliwa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Hidatsa: Coding = {
  code: '1269-0',
  display: 'Hidatsa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Hoopa: Coding = {
  code: '1271-6',
  display: 'Hoopa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Trinity: Coding = {
  code: '1272-4',
  display: 'Trinity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Whilkut: Coding = {
  code: '1273-2',
  display: 'Whilkut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_HoopaExtension: Coding = {
  code: '1275-7',
  display: 'Hoopa Extension',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Houma: Coding = {
  code: '1277-3',
  display: 'Houma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_InajaCosmit: Coding = {
  code: '1279-9',
  display: 'Inaja-Cosmit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Iowa: Coding = {
  code: '1281-5',
  display: 'Iowa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_IowaOfKansasNebraska: Coding = {
  code: '1282-3',
  display: 'Iowa of Kansas-Nebraska',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_IowaOfOklahoma: Coding = {
  code: '1283-1',
  display: 'Iowa of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Iroquois: Coding = {
  code: '1285-6',
  display: 'Iroquois',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cayuga: Coding = {
  code: '1286-4',
  display: 'Cayuga',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Mohawk: Coding = {
  code: '1287-2',
  display: 'Mohawk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Oneida: Coding = {
  code: '1288-0',
  display: 'Oneida',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Onondaga: Coding = {
  code: '1289-8',
  display: 'Onondaga',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Seneca: Coding = {
  code: '1290-6',
  display: 'Seneca',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SenecaNation: Coding = {
  code: '1291-4',
  display: 'Seneca Nation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SenecaCayuga: Coding = {
  code: '1292-2',
  display: 'Seneca-Cayuga',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TonawandaSeneca: Coding = {
  code: '1293-0',
  display: 'Tonawanda Seneca',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tuscarora: Coding = {
  code: '1294-8',
  display: 'Tuscarora',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wyandotte: Coding = {
  code: '1295-5',
  display: 'Wyandotte',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Juaneno: Coding = {
  code: '1297-1',
  display: 'Juaneno',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kalispel: Coding = {
  code: '1299-7',
  display: 'Kalispel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Karuk: Coding = {
  code: '1301-1',
  display: 'Karuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kaw: Coding = {
  code: '1303-7',
  display: 'Kaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kickapoo: Coding = {
  code: '1305-2',
  display: 'Kickapoo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaKickapoo: Coding = {
  code: '1306-0',
  display: 'Oklahoma Kickapoo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TexasKickapoo: Coding = {
  code: '1307-8',
  display: 'Texas Kickapoo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kiowa: Coding = {
  code: '1309-4',
  display: 'Kiowa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaKiowa: Coding = {
  code: '1310-2',
  display: 'Oklahoma Kiowa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Klallam: Coding = {
  code: '1312-8',
  display: 'Klallam',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Jamestown: Coding = {
  code: '1313-6',
  display: 'Jamestown',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LowerElwha: Coding = {
  code: '1314-4',
  display: 'Lower Elwha',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PortGambleKlallam: Coding = {
  code: '1315-1',
  display: 'Port Gamble Klallam',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Klamath: Coding = {
  code: '1317-7',
  display: 'Klamath',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Konkow: Coding = {
  code: '1319-3',
  display: 'Konkow',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kootenai: Coding = {
  code: '1321-9',
  display: 'Kootenai',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Lassik: Coding = {
  code: '1323-5',
  display: 'Lassik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LongIsland: Coding = {
  code: '1325-0',
  display: 'Long Island',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Matinecock: Coding = {
  code: '1326-8',
  display: 'Matinecock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Montauk: Coding = {
  code: '1327-6',
  display: 'Montauk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Poospatuck: Coding = {
  code: '1328-4',
  display: 'Poospatuck',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Setauket: Coding = {
  code: '1329-2',
  display: 'Setauket',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Luiseno: Coding = {
  code: '1331-8',
  display: 'Luiseno',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LaJolla: Coding = {
  code: '1332-6',
  display: 'La Jolla',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Pala: Coding = {
  code: '1333-4',
  display: 'Pala',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Pauma: Coding = {
  code: '1334-2',
  display: 'Pauma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Pechanga: Coding = {
  code: '1335-9',
  display: 'Pechanga',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Soboba: Coding = {
  code: '1336-7',
  display: 'Soboba',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TwentyNinePalms: Coding = {
  code: '1337-5',
  display: 'Twenty-Nine Palms',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Temecula: Coding = {
  code: '1338-3',
  display: 'Temecula',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Lumbee: Coding = {
  code: '1340-9',
  display: 'Lumbee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Lummi: Coding = {
  code: '1342-5',
  display: 'Lummi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Maidu: Coding = {
  code: '1344-1',
  display: 'Maidu',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MountainMaidu: Coding = {
  code: '1345-8',
  display: 'Mountain Maidu',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nishinam: Coding = {
  code: '1346-6',
  display: 'Nishinam',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Makah: Coding = {
  code: '1348-2',
  display: 'Makah',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Maliseet: Coding = {
  code: '1350-8',
  display: 'Maliseet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Mandan: Coding = {
  code: '1352-4',
  display: 'Mandan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Mattaponi: Coding = {
  code: '1354-0',
  display: 'Mattaponi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Menominee: Coding = {
  code: '1356-5',
  display: 'Menominee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Miami: Coding = {
  code: '1358-1',
  display: 'Miami',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_IllinoisMiami: Coding = {
  code: '1359-9',
  display: 'Illinois Miami',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_IndianaMiami: Coding = {
  code: '1360-7',
  display: 'Indiana Miami',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaMiami: Coding = {
  code: '1361-5',
  display: 'Oklahoma Miami',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Miccosukee: Coding = {
  code: '1363-1',
  display: 'Miccosukee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Micmac: Coding = {
  code: '1365-6',
  display: 'Micmac',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Aroostook: Coding = {
  code: '1366-4',
  display: 'Aroostook',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MissionIndians: Coding = {
  code: '1368-0',
  display: 'Mission Indians',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Miwok: Coding = {
  code: '1370-6',
  display: 'Miwok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Modoc: Coding = {
  code: '1372-2',
  display: 'Modoc',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Mohegan: Coding = {
  code: '1374-8',
  display: 'Mohegan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Mono: Coding = {
  code: '1376-3',
  display: 'Mono',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nanticoke: Coding = {
  code: '1378-9',
  display: 'Nanticoke',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Narragansett: Coding = {
  code: '1380-5',
  display: 'Narragansett',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Navajo: Coding = {
  code: '1382-1',
  display: 'Navajo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AlamoNavajo: Coding = {
  code: '1383-9',
  display: 'Alamo Navajo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CanoncitoNavajo: Coding = {
  code: '1384-7',
  display: 'Canoncito Navajo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_RamahNavajo: Coding = {
  code: '1385-4',
  display: 'Ramah Navajo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NezPerce: Coding = {
  code: '1387-0',
  display: 'Nez Perce',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nomalaki: Coding = {
  code: '1389-6',
  display: 'Nomalaki',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NorthwestTribes: Coding = {
  code: '1391-2',
  display: 'Northwest Tribes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Alsea: Coding = {
  code: '1392-0',
  display: 'Alsea',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Celilo: Coding = {
  code: '1393-8',
  display: 'Celilo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Columbia: Coding = {
  code: '1394-6',
  display: 'Columbia',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kalapuya: Coding = {
  code: '1395-3',
  display: 'Kalapuya',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Molala: Coding = {
  code: '1396-1',
  display: 'Molala',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Talakamish: Coding = {
  code: '1397-9',
  display: 'Talakamish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tenino: Coding = {
  code: '1398-7',
  display: 'Tenino',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tillamook: Coding = {
  code: '1399-5',
  display: 'Tillamook',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wenatchee: Coding = {
  code: '1400-1',
  display: 'Wenatchee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Yahooskin: Coding = {
  code: '1401-9',
  display: 'Yahooskin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Omaha: Coding = {
  code: '1403-5',
  display: 'Omaha',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OregonAthabaskan: Coding = {
  code: '1405-0',
  display: 'Oregon Athabaskan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Osage: Coding = {
  code: '1407-6',
  display: 'Osage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OtoeMissouria: Coding = {
  code: '1409-2',
  display: 'Otoe-Missouria',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ottawa: Coding = {
  code: '1411-8',
  display: 'Ottawa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BurtLakeOttawa: Coding = {
  code: '1412-6',
  display: 'Burt Lake Ottawa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MichiganOttawa: Coding = {
  code: '1413-4',
  display: 'Michigan Ottawa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaOttawa: Coding = {
  code: '1414-2',
  display: 'Oklahoma Ottawa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Paiute: Coding = {
  code: '1416-7',
  display: 'Paiute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Bishop: Coding = {
  code: '1417-5',
  display: 'Bishop',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Bridgeport: Coding = {
  code: '1418-3',
  display: 'Bridgeport',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BurnsPaiute: Coding = {
  code: '1419-1',
  display: 'Burns Paiute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cedarville: Coding = {
  code: '1420-9',
  display: 'Cedarville',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FortBidwell: Coding = {
  code: '1421-7',
  display: 'Fort Bidwell',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FortIndependence: Coding = {
  code: '1422-5',
  display: 'Fort Independence',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kaibab: Coding = {
  code: '1423-3',
  display: 'Kaibab',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LasVegas: Coding = {
  code: '1424-1',
  display: 'Las Vegas',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LonePine: Coding = {
  code: '1425-8',
  display: 'Lone Pine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Lovelock: Coding = {
  code: '1426-6',
  display: 'Lovelock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MalheurPaiute: Coding = {
  code: '1427-4',
  display: 'Malheur Paiute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Moapa: Coding = {
  code: '1428-2',
  display: 'Moapa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NorthernPaiute: Coding = {
  code: '1429-0',
  display: 'Northern Paiute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OwensValley: Coding = {
  code: '1430-8',
  display: 'Owens Valley',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PyramidLake: Coding = {
  code: '1431-6',
  display: 'Pyramid Lake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SanJuanSouthernPaiute: Coding = {
  code: '1432-4',
  display: 'San Juan Southern Paiute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SouthernPaiute: Coding = {
  code: '1433-2',
  display: 'Southern Paiute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SummitLake: Coding = {
  code: '1434-0',
  display: 'Summit Lake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_UtuUtuGwaituPaiute: Coding = {
  code: '1435-7',
  display: 'Utu Utu Gwaitu Paiute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WalkerRiver: Coding = {
  code: '1436-5',
  display: 'Walker River',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_YeringtonPaiute: Coding = {
  code: '1437-3',
  display: 'Yerington Paiute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Pamunkey: Coding = {
  code: '1439-9',
  display: 'Pamunkey',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Passamaquoddy: Coding = {
  code: '1441-5',
  display: 'Passamaquoddy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_IndianTownship: Coding = {
  code: '1442-3',
  display: 'Indian Township',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PleasantPointPassamaquoddy: Coding = {
  code: '1443-1',
  display: 'Pleasant Point Passamaquoddy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Pawnee: Coding = {
  code: '1445-6',
  display: 'Pawnee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaPawnee: Coding = {
  code: '1446-4',
  display: 'Oklahoma Pawnee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Penobscot: Coding = {
  code: '1448-0',
  display: 'Penobscot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Peoria: Coding = {
  code: '1450-6',
  display: 'Peoria',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaPeoria: Coding = {
  code: '1451-4',
  display: 'Oklahoma Peoria',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Pequot: Coding = {
  code: '1453-0',
  display: 'Pequot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MarshantucketPequot: Coding = {
  code: '1454-8',
  display: 'Marshantucket Pequot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Pima: Coding = {
  code: '1456-3',
  display: 'Pima',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_GilaRiverPimaMaricopa: Coding = {
  code: '1457-1',
  display: 'Gila River Pima-Maricopa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SaltRiverPimaMaricopa: Coding = {
  code: '1458-9',
  display: 'Salt River Pima-Maricopa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Piscataway: Coding = {
  code: '1460-5',
  display: 'Piscataway',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PitRiver: Coding = {
  code: '1462-1',
  display: 'Pit River',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Pomo: Coding = {
  code: '1464-7',
  display: 'Pomo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CentralPomo: Coding = {
  code: '1465-4',
  display: 'Central Pomo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_DryCreek: Coding = {
  code: '1466-2',
  display: 'Dry Creek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_EasternPomo: Coding = {
  code: '1467-0',
  display: 'Eastern Pomo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kashia: Coding = {
  code: '1468-8',
  display: 'Kashia',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NorthernPomo: Coding = {
  code: '1469-6',
  display: 'Northern Pomo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ScottsValley: Coding = {
  code: '1470-4',
  display: 'Scotts Valley',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Stonyford: Coding = {
  code: '1471-2',
  display: 'Stonyford',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SulphurBank: Coding = {
  code: '1472-0',
  display: 'Sulphur Bank',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ponca: Coding = {
  code: '1474-6',
  display: 'Ponca',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NebraskaPonca: Coding = {
  code: '1475-3',
  display: 'Nebraska Ponca',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaPonca: Coding = {
  code: '1476-1',
  display: 'Oklahoma Ponca',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Potawatomi: Coding = {
  code: '1478-7',
  display: 'Potawatomi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CitizenBandPotawatomi: Coding = {
  code: '1479-5',
  display: 'Citizen Band Potawatomi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ForestCounty: Coding = {
  code: '1480-3',
  display: 'Forest County',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Hannahville: Coding = {
  code: '1481-1',
  display: 'Hannahville',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_HuronPotawatomi: Coding = {
  code: '1482-9',
  display: 'Huron Potawatomi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PokagonPotawatomi: Coding = {
  code: '1483-7',
  display: 'Pokagon Potawatomi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PrairieBand: Coding = {
  code: '1484-5',
  display: 'Prairie Band',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WisconsinPotawatomi: Coding = {
  code: '1485-2',
  display: 'Wisconsin Potawatomi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Powhatan: Coding = {
  code: '1487-8',
  display: 'Powhatan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Pueblo: Coding = {
  code: '1489-4',
  display: 'Pueblo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Acoma: Coding = {
  code: '1490-2',
  display: 'Acoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ArizonaTewa: Coding = {
  code: '1491-0',
  display: 'Arizona Tewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cochiti: Coding = {
  code: '1492-8',
  display: 'Cochiti',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Hopi: Coding = {
  code: '1493-6',
  display: 'Hopi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Isleta: Coding = {
  code: '1494-4',
  display: 'Isleta',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Jemez: Coding = {
  code: '1495-1',
  display: 'Jemez',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Keres: Coding = {
  code: '1496-9',
  display: 'Keres',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Laguna: Coding = {
  code: '1497-7',
  display: 'Laguna',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nambe: Coding = {
  code: '1498-5',
  display: 'Nambe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Picuris: Coding = {
  code: '1499-3',
  display: 'Picuris',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Piro: Coding = {
  code: '1500-8',
  display: 'Piro',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Pojoaque: Coding = {
  code: '1501-6',
  display: 'Pojoaque',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SanFelipe: Coding = {
  code: '1502-4',
  display: 'San Felipe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SanIldefonso: Coding = {
  code: '1503-2',
  display: 'San Ildefonso',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SanJuanPueblo: Coding = {
  code: '1504-0',
  display: 'San Juan Pueblo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SanJuanDe: Coding = {
  code: '1505-7',
  display: 'San Juan De',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SanJuan: Coding = {
  code: '1506-5',
  display: 'San Juan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Sandia: Coding = {
  code: '1507-3',
  display: 'Sandia',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SantaAna: Coding = {
  code: '1508-1',
  display: 'Santa Ana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SantaClara: Coding = {
  code: '1509-9',
  display: 'Santa Clara',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SantoDomingo: Coding = {
  code: '1510-7',
  display: 'Santo Domingo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Taos: Coding = {
  code: '1511-5',
  display: 'Taos',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tesuque: Coding = {
  code: '1512-3',
  display: 'Tesuque',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tewa: Coding = {
  code: '1513-1',
  display: 'Tewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tigua: Coding = {
  code: '1514-9',
  display: 'Tigua',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Zia: Coding = {
  code: '1515-6',
  display: 'Zia',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Zuni: Coding = {
  code: '1516-4',
  display: 'Zuni',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PugetSoundSalish: Coding = {
  code: '1518-0',
  display: 'Puget Sound Salish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Duwamish: Coding = {
  code: '1519-8',
  display: 'Duwamish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kikiallus: Coding = {
  code: '1520-6',
  display: 'Kikiallus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LowerSkagit: Coding = {
  code: '1521-4',
  display: 'Lower Skagit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Muckleshoot: Coding = {
  code: '1522-2',
  display: 'Muckleshoot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nisqually: Coding = {
  code: '1523-0',
  display: 'Nisqually',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nooksack: Coding = {
  code: '1524-8',
  display: 'Nooksack',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PortMadison: Coding = {
  code: '1525-5',
  display: 'Port Madison',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Puyallup: Coding = {
  code: '1526-3',
  display: 'Puyallup',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Samish: Coding = {
  code: '1527-1',
  display: 'Samish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SaukSuiattle: Coding = {
  code: '1528-9',
  display: 'Sauk-Suiattle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Skokomish: Coding = {
  code: '1529-7',
  display: 'Skokomish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Skykomish: Coding = {
  code: '1530-5',
  display: 'Skykomish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Snohomish: Coding = {
  code: '1531-3',
  display: 'Snohomish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Snoqualmie: Coding = {
  code: '1532-1',
  display: 'Snoqualmie',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SquaxinIsland: Coding = {
  code: '1533-9',
  display: 'Squaxin Island',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Steilacoom: Coding = {
  code: '1534-7',
  display: 'Steilacoom',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Stillaguamish: Coding = {
  code: '1535-4',
  display: 'Stillaguamish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Suquamish: Coding = {
  code: '1536-2',
  display: 'Suquamish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Swinomish: Coding = {
  code: '1537-0',
  display: 'Swinomish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tulalip: Coding = {
  code: '1538-8',
  display: 'Tulalip',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_UpperSkagit: Coding = {
  code: '1539-6',
  display: 'Upper Skagit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Quapaw: Coding = {
  code: '1541-2',
  display: 'Quapaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Quinault: Coding = {
  code: '1543-8',
  display: 'Quinault',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Rappahannock: Coding = {
  code: '1545-3',
  display: 'Rappahannock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_RenoSparks: Coding = {
  code: '1547-9',
  display: 'Reno-Sparks',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_RoundValley: Coding = {
  code: '1549-5',
  display: 'Round Valley',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SacAndFox: Coding = {
  code: '1551-1',
  display: 'Sac and Fox',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_IowaSacAndFox: Coding = {
  code: '1552-9',
  display: 'Iowa Sac and Fox',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MissouriSacAndFox: Coding = {
  code: '1553-7',
  display: 'Missouri Sac and Fox',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaSacAndFox: Coding = {
  code: '1554-5',
  display: 'Oklahoma Sac and Fox',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Salinan: Coding = {
  code: '1556-0',
  display: 'Salinan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Salish: Coding = {
  code: '1558-6',
  display: 'Salish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SalishAndKootenai: Coding = {
  code: '1560-2',
  display: 'Salish and Kootenai',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Schaghticoke: Coding = {
  code: '1562-8',
  display: 'Schaghticoke',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ScottValley: Coding = {
  code: '1564-4',
  display: 'Scott Valley',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Seminole: Coding = {
  code: '1566-9',
  display: 'Seminole',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BigCypress: Coding = {
  code: '1567-7',
  display: 'Big Cypress',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Brighton: Coding = {
  code: '1568-5',
  display: 'Brighton',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FloridaSeminole: Coding = {
  code: '1569-3',
  display: 'Florida Seminole',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_HollywoodSeminole: Coding = {
  code: '1570-1',
  display: 'Hollywood Seminole',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OklahomaSeminole: Coding = {
  code: '1571-9',
  display: 'Oklahoma Seminole',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Serrano: Coding = {
  code: '1573-5',
  display: 'Serrano',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SanManual: Coding = {
  code: '1574-3',
  display: 'San Manual',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Shasta: Coding = {
  code: '1576-8',
  display: 'Shasta',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Shawnee: Coding = {
  code: '1578-4',
  display: 'Shawnee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AbsenteeShawnee: Coding = {
  code: '1579-2',
  display: 'Absentee Shawnee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_EasternShawnee: Coding = {
  code: '1580-0',
  display: 'Eastern Shawnee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Shinnecock: Coding = {
  code: '1582-6',
  display: 'Shinnecock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ShoalwaterBay: Coding = {
  code: '1584-2',
  display: 'Shoalwater Bay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Shoshone: Coding = {
  code: '1586-7',
  display: 'Shoshone',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BattleMountain: Coding = {
  code: '1587-5',
  display: 'Battle Mountain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Duckwater: Coding = {
  code: '1588-3',
  display: 'Duckwater',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Elko: Coding = {
  code: '1589-1',
  display: 'Elko',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ely: Coding = {
  code: '1590-9',
  display: 'Ely',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Goshute: Coding = {
  code: '1591-7',
  display: 'Goshute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Panamint: Coding = {
  code: '1592-5',
  display: 'Panamint',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_RubyValley: Coding = {
  code: '1593-3',
  display: 'Ruby Valley',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SkullValley: Coding = {
  code: '1594-1',
  display: 'Skull Valley',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SouthForkShoshone: Coding = {
  code: '1595-8',
  display: 'South Fork Shoshone',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TeMoakWesternShoshone: Coding = {
  code: '1596-6',
  display: 'Te-Moak Western Shoshone',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TimbiShaShoshone: Coding = {
  code: '1597-4',
  display: 'Timbi-Sha Shoshone',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Washakie: Coding = {
  code: '1598-2',
  display: 'Washakie',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WindRiverShoshone: Coding = {
  code: '1599-0',
  display: 'Wind River Shoshone',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Yomba: Coding = {
  code: '1600-6',
  display: 'Yomba',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ShoshonePaiute: Coding = {
  code: '1602-2',
  display: 'Shoshone Paiute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_DuckValley: Coding = {
  code: '1603-0',
  display: 'Duck Valley',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Fallon: Coding = {
  code: '1604-8',
  display: 'Fallon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FortMcDermitt: Coding = {
  code: '1605-5',
  display: 'Fort McDermitt',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Siletz: Coding = {
  code: '1607-1',
  display: 'Siletz',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Sioux: Coding = {
  code: '1609-7',
  display: 'Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BlackfootSioux: Coding = {
  code: '1610-5',
  display: 'Blackfoot Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BruleSioux: Coding = {
  code: '1611-3',
  display: 'Brule Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CheyenneRiverSioux: Coding = {
  code: '1612-1',
  display: 'Cheyenne River Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CrowCreekSioux: Coding = {
  code: '1613-9',
  display: 'Crow Creek Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_DakotaSioux: Coding = {
  code: '1614-7',
  display: 'Dakota Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FlandreauSantee: Coding = {
  code: '1615-4',
  display: 'Flandreau Santee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FortPeck: Coding = {
  code: '1616-2',
  display: 'Fort Peck',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LakeTraverseSioux: Coding = {
  code: '1617-0',
  display: 'Lake Traverse Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LowerBruleSioux: Coding = {
  code: '1618-8',
  display: 'Lower Brule Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LowerSioux: Coding = {
  code: '1619-6',
  display: 'Lower Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MdewakantonSioux: Coding = {
  code: '1620-4',
  display: 'Mdewakanton Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Miniconjou: Coding = {
  code: '1621-2',
  display: 'Miniconjou',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OglalaSioux: Coding = {
  code: '1622-0',
  display: 'Oglala Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PineRidgeSioux: Coding = {
  code: '1623-8',
  display: 'Pine Ridge Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PipestoneSioux: Coding = {
  code: '1624-6',
  display: 'Pipestone Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PrairieIslandSioux: Coding = {
  code: '1625-3',
  display: 'Prairie Island Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PriorLakeSioux: Coding = {
  code: '1626-1',
  display: 'Prior Lake Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_RosebudSioux: Coding = {
  code: '1627-9',
  display: 'Rosebud Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SansArcSioux: Coding = {
  code: '1628-7',
  display: 'Sans Arc Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SanteeSioux: Coding = {
  code: '1629-5',
  display: 'Santee Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SissetonWahpeton: Coding = {
  code: '1630-3',
  display: 'Sisseton-Wahpeton',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SissetonSioux: Coding = {
  code: '1631-1',
  display: 'Sisseton Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SpiritLakeSioux: Coding = {
  code: '1632-9',
  display: 'Spirit Lake Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_StandingRockSioux: Coding = {
  code: '1633-7',
  display: 'Standing Rock Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TetonSioux: Coding = {
  code: '1634-5',
  display: 'Teton Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TwoKettleSioux: Coding = {
  code: '1635-2',
  display: 'Two Kettle Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_UpperSioux: Coding = {
  code: '1636-0',
  display: 'Upper Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WahpekuteSioux: Coding = {
  code: '1637-8',
  display: 'Wahpekute Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WahpetonSioux: Coding = {
  code: '1638-6',
  display: 'Wahpeton Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WazhazaSioux: Coding = {
  code: '1639-4',
  display: 'Wazhaza Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_YanktonSioux: Coding = {
  code: '1640-2',
  display: 'Yankton Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_YanktonaiSioux: Coding = {
  code: '1641-0',
  display: 'Yanktonai Sioux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Siuslaw: Coding = {
  code: '1643-6',
  display: 'Siuslaw',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Spokane: Coding = {
  code: '1645-1',
  display: 'Spokane',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Stewart: Coding = {
  code: '1647-7',
  display: 'Stewart',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Stockbridge: Coding = {
  code: '1649-3',
  display: 'Stockbridge',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Susanville: Coding = {
  code: '1651-9',
  display: 'Susanville',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TohonoOQuoteOdham: Coding = {
  code: '1653-5',
  display: "Tohono O'Odham",
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AkChin: Coding = {
  code: '1654-3',
  display: 'Ak-Chin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_GilaBend: Coding = {
  code: '1655-0',
  display: 'Gila Bend',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SanXavier: Coding = {
  code: '1656-8',
  display: 'San Xavier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Sells: Coding = {
  code: '1657-6',
  display: 'Sells',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tolowa: Coding = {
  code: '1659-2',
  display: 'Tolowa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tonkawa: Coding = {
  code: '1661-8',
  display: 'Tonkawa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tygh: Coding = {
  code: '1663-4',
  display: 'Tygh',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Umatilla: Coding = {
  code: '1665-9',
  display: 'Umatilla',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Umpqua: Coding = {
  code: '1667-5',
  display: 'Umpqua',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CowCreekUmpqua: Coding = {
  code: '1668-3',
  display: 'Cow Creek Umpqua',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ute: Coding = {
  code: '1670-9',
  display: 'Ute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AllenCanyon: Coding = {
  code: '1671-7',
  display: 'Allen Canyon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_UintahUte: Coding = {
  code: '1672-5',
  display: 'Uintah Ute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_UteMountainUte: Coding = {
  code: '1673-3',
  display: 'Ute Mountain Ute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wailaki: Coding = {
  code: '1675-8',
  display: 'Wailaki',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WallaWalla: Coding = {
  code: '1677-4',
  display: 'Walla-Walla',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wampanoag: Coding = {
  code: '1679-0',
  display: 'Wampanoag',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_GayHeadWampanoag: Coding = {
  code: '1680-8',
  display: 'Gay Head Wampanoag',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MashpeeWampanoag: Coding = {
  code: '1681-6',
  display: 'Mashpee Wampanoag',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WarmSprings: Coding = {
  code: '1683-2',
  display: 'Warm Springs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wascopum: Coding = {
  code: '1685-7',
  display: 'Wascopum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Washoe: Coding = {
  code: '1687-3',
  display: 'Washoe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Alpine: Coding = {
  code: '1688-1',
  display: 'Alpine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Carson: Coding = {
  code: '1689-9',
  display: 'Carson',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Dresslerville: Coding = {
  code: '1690-7',
  display: 'Dresslerville',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wichita: Coding = {
  code: '1692-3',
  display: 'Wichita',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WindRiver: Coding = {
  code: '1694-9',
  display: 'Wind River',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Winnebago: Coding = {
  code: '1696-4',
  display: 'Winnebago',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_HoChunk: Coding = {
  code: '1697-2',
  display: 'Ho-chunk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NebraskaWinnebago: Coding = {
  code: '1698-0',
  display: 'Nebraska Winnebago',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Winnemucca: Coding = {
  code: '1700-4',
  display: 'Winnemucca',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wintun: Coding = {
  code: '1702-0',
  display: 'Wintun',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wiyot: Coding = {
  code: '1704-6',
  display: 'Wiyot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TableBluff: Coding = {
  code: '1705-3',
  display: 'Table Bluff',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Yakama: Coding = {
  code: '1707-9',
  display: 'Yakama',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_YakamaCowlitz: Coding = {
  code: '1709-5',
  display: 'Yakama Cowlitz',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Yaqui: Coding = {
  code: '1711-1',
  display: 'Yaqui',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BarrioLibre: Coding = {
  code: '1712-9',
  display: 'Barrio Libre',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PascuaYaqui: Coding = {
  code: '1713-7',
  display: 'Pascua Yaqui',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_YavapaiApache: Coding = {
  code: '1715-2',
  display: 'Yavapai Apache',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Yokuts: Coding = {
  code: '1717-8',
  display: 'Yokuts',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chukchansi: Coding = {
  code: '1718-6',
  display: 'Chukchansi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tachi: Coding = {
  code: '1719-4',
  display: 'Tachi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TuleRiver: Coding = {
  code: '1720-2',
  display: 'Tule River',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Yuchi: Coding = {
  code: '1722-8',
  display: 'Yuchi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Yuman: Coding = {
  code: '1724-4',
  display: 'Yuman',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cocopah: Coding = {
  code: '1725-1',
  display: 'Cocopah',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Havasupai: Coding = {
  code: '1726-9',
  display: 'Havasupai',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Hualapai: Coding = {
  code: '1727-7',
  display: 'Hualapai',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Maricopa: Coding = {
  code: '1728-5',
  display: 'Maricopa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Mohave: Coding = {
  code: '1729-3',
  display: 'Mohave',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Quechan: Coding = {
  code: '1730-1',
  display: 'Quechan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Yavapai: Coding = {
  code: '1731-9',
  display: 'Yavapai',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Yurok: Coding = {
  code: '1732-7',
  display: 'Yurok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CoastYurok: Coding = {
  code: '1733-5',
  display: 'Coast Yurok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AlaskaNative: Coding = {
  code: '1735-0',
  display: 'Alaska Native',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AlaskaIndian: Coding = {
  code: '1737-6',
  display: 'Alaska Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AlaskanAthabascan: Coding = {
  code: '1739-2',
  display: 'Alaskan Athabascan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ahtna: Coding = {
  code: '1740-0',
  display: 'Ahtna',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Alatna: Coding = {
  code: '1741-8',
  display: 'Alatna',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Alexander: Coding = {
  code: '1742-6',
  display: 'Alexander',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Allakaket: Coding = {
  code: '1743-4',
  display: 'Allakaket',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Alanvik: Coding = {
  code: '1744-2',
  display: 'Alanvik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Anvik: Coding = {
  code: '1745-9',
  display: 'Anvik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Arctic: Coding = {
  code: '1746-7',
  display: 'Arctic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Beaver: Coding = {
  code: '1747-5',
  display: 'Beaver',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BirchCreek: Coding = {
  code: '1748-3',
  display: 'Birch Creek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cantwell: Coding = {
  code: '1749-1',
  display: 'Cantwell',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chalkyitsik: Coding = {
  code: '1750-9',
  display: 'Chalkyitsik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chickaloon: Coding = {
  code: '1751-7',
  display: 'Chickaloon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chistochina: Coding = {
  code: '1752-5',
  display: 'Chistochina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chitina: Coding = {
  code: '1753-3',
  display: 'Chitina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Circle: Coding = {
  code: '1754-1',
  display: 'Circle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CookInlet: Coding = {
  code: '1755-8',
  display: 'Cook Inlet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CopperCenter: Coding = {
  code: '1756-6',
  display: 'Copper Center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CopperRiver: Coding = {
  code: '1757-4',
  display: 'Copper River',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_DotLake: Coding = {
  code: '1758-2',
  display: 'Dot Lake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Doyon: Coding = {
  code: '1759-0',
  display: 'Doyon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Eagle: Coding = {
  code: '1760-8',
  display: 'Eagle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Eklutna: Coding = {
  code: '1761-6',
  display: 'Eklutna',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Evansville: Coding = {
  code: '1762-4',
  display: 'Evansville',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FortYukon: Coding = {
  code: '1763-2',
  display: 'Fort Yukon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Gakona: Coding = {
  code: '1764-0',
  display: 'Gakona',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Galena: Coding = {
  code: '1765-7',
  display: 'Galena',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Grayling: Coding = {
  code: '1766-5',
  display: 'Grayling',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Gulkana: Coding = {
  code: '1767-3',
  display: 'Gulkana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_HealyLake: Coding = {
  code: '1768-1',
  display: 'Healy Lake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_HolyCross: Coding = {
  code: '1769-9',
  display: 'Holy Cross',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Hughes: Coding = {
  code: '1770-7',
  display: 'Hughes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Huslia: Coding = {
  code: '1771-5',
  display: 'Huslia',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Iliamna: Coding = {
  code: '1772-3',
  display: 'Iliamna',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kaltag: Coding = {
  code: '1773-1',
  display: 'Kaltag',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_KlutiKaah: Coding = {
  code: '1774-9',
  display: 'Kluti Kaah',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Knik: Coding = {
  code: '1775-6',
  display: 'Knik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Koyukuk: Coding = {
  code: '1776-4',
  display: 'Koyukuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LakeMinchumina: Coding = {
  code: '1777-2',
  display: 'Lake Minchumina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Lime: Coding = {
  code: '1778-0',
  display: 'Lime',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Mcgrath: Coding = {
  code: '1779-8',
  display: 'Mcgrath',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ManleyHotSprings: Coding = {
  code: '1780-6',
  display: 'Manley Hot Springs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MentastaLake: Coding = {
  code: '1781-4',
  display: 'Mentasta Lake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Minto: Coding = {
  code: '1782-2',
  display: 'Minto',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nenana: Coding = {
  code: '1783-0',
  display: 'Nenana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nikolai: Coding = {
  code: '1784-8',
  display: 'Nikolai',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ninilchik: Coding = {
  code: '1785-5',
  display: 'Ninilchik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nondalton: Coding = {
  code: '1786-3',
  display: 'Nondalton',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Northway: Coding = {
  code: '1787-1',
  display: 'Northway',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nulato: Coding = {
  code: '1788-9',
  display: 'Nulato',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PedroBay: Coding = {
  code: '1789-7',
  display: 'Pedro Bay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Rampart: Coding = {
  code: '1790-5',
  display: 'Rampart',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ruby: Coding = {
  code: '1791-3',
  display: 'Ruby',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Salamatof: Coding = {
  code: '1792-1',
  display: 'Salamatof',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Seldovia: Coding = {
  code: '1793-9',
  display: 'Seldovia',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Slana: Coding = {
  code: '1794-7',
  display: 'Slana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Shageluk: Coding = {
  code: '1795-4',
  display: 'Shageluk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Stevens: Coding = {
  code: '1796-2',
  display: 'Stevens',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_StonyRiver: Coding = {
  code: '1797-0',
  display: 'Stony River',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Takotna: Coding = {
  code: '1798-8',
  display: 'Takotna',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tanacross: Coding = {
  code: '1799-6',
  display: 'Tanacross',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tanaina: Coding = {
  code: '1800-2',
  display: 'Tanaina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tanana: Coding = {
  code: '1801-0',
  display: 'Tanana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TananaChiefs: Coding = {
  code: '1802-8',
  display: 'Tanana Chiefs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tazlina: Coding = {
  code: '1803-6',
  display: 'Tazlina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Telida: Coding = {
  code: '1804-4',
  display: 'Telida',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tetlin: Coding = {
  code: '1805-1',
  display: 'Tetlin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tok: Coding = {
  code: '1806-9',
  display: 'Tok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tyonek: Coding = {
  code: '1807-7',
  display: 'Tyonek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Venetie: Coding = {
  code: '1808-5',
  display: 'Venetie',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wiseman: Coding = {
  code: '1809-3',
  display: 'Wiseman',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SoutheastAlaska: Coding = {
  code: '1811-9',
  display: 'Southeast Alaska',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TlingitHaida: Coding = {
  code: '1813-5',
  display: 'Tlingit-Haida',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Angoon: Coding = {
  code: '1814-3',
  display: 'Angoon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CentralCouncilOfTlingitAndHaidaTribes: Coding = {
  code: '1815-0',
  display: 'Central Council of Tlingit and Haida Tribes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chilkat: Coding = {
  code: '1816-8',
  display: 'Chilkat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chilkoot: Coding = {
  code: '1817-6',
  display: 'Chilkoot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Craig: Coding = {
  code: '1818-4',
  display: 'Craig',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Douglas: Coding = {
  code: '1819-2',
  display: 'Douglas',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Haida: Coding = {
  code: '1820-0',
  display: 'Haida',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Hoonah: Coding = {
  code: '1821-8',
  display: 'Hoonah',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Hydaburg: Coding = {
  code: '1822-6',
  display: 'Hydaburg',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kake: Coding = {
  code: '1823-4',
  display: 'Kake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kasaan: Coding = {
  code: '1824-2',
  display: 'Kasaan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kenaitze: Coding = {
  code: '1825-9',
  display: 'Kenaitze',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ketchikan: Coding = {
  code: '1826-7',
  display: 'Ketchikan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Klawock: Coding = {
  code: '1827-5',
  display: 'Klawock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Pelican: Coding = {
  code: '1828-3',
  display: 'Pelican',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Petersburg: Coding = {
  code: '1829-1',
  display: 'Petersburg',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Saxman: Coding = {
  code: '1830-9',
  display: 'Saxman',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Sitka: Coding = {
  code: '1831-7',
  display: 'Sitka',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TenakeeSprings: Coding = {
  code: '1832-5',
  display: 'Tenakee Springs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tlingit: Coding = {
  code: '1833-3',
  display: 'Tlingit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wrangell: Coding = {
  code: '1834-1',
  display: 'Wrangell',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Yakutat: Coding = {
  code: '1835-8',
  display: 'Yakutat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tsimshian: Coding = {
  code: '1837-4',
  display: 'Tsimshian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Metlakatla: Coding = {
  code: '1838-2',
  display: 'Metlakatla',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Eskimo: Coding = {
  code: '1840-8',
  display: 'Eskimo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_GreenlandEskimo: Coding = {
  code: '1842-4',
  display: 'Greenland Eskimo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_InupiatEskimo: Coding = {
  code: '1844-0',
  display: 'Inupiat Eskimo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ambler: Coding = {
  code: '1845-7',
  display: 'Ambler',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Anaktuvuk: Coding = {
  code: '1846-5',
  display: 'Anaktuvuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AnaktuvukPass: Coding = {
  code: '1847-3',
  display: 'Anaktuvuk Pass',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ArcticSlopeInupiat: Coding = {
  code: '1848-1',
  display: 'Arctic Slope Inupiat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ArcticSlopeCorporation: Coding = {
  code: '1849-9',
  display: 'Arctic Slope Corporation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Atqasuk: Coding = {
  code: '1850-7',
  display: 'Atqasuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Barrow: Coding = {
  code: '1851-5',
  display: 'Barrow',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BeringStraitsInupiat: Coding = {
  code: '1852-3',
  display: 'Bering Straits Inupiat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BrevigMission: Coding = {
  code: '1853-1',
  display: 'Brevig Mission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Buckland: Coding = {
  code: '1854-9',
  display: 'Buckland',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chinik: Coding = {
  code: '1855-6',
  display: 'Chinik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Council: Coding = {
  code: '1856-4',
  display: 'Council',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Deering: Coding = {
  code: '1857-2',
  display: 'Deering',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Elim: Coding = {
  code: '1858-0',
  display: 'Elim',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Golovin: Coding = {
  code: '1859-8',
  display: 'Golovin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_InalikDiomede: Coding = {
  code: '1860-6',
  display: 'Inalik Diomede',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Inupiaq: Coding = {
  code: '1861-4',
  display: 'Inupiaq',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kaktovik: Coding = {
  code: '1862-2',
  display: 'Kaktovik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kawerak: Coding = {
  code: '1863-0',
  display: 'Kawerak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kiana: Coding = {
  code: '1864-8',
  display: 'Kiana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kivalina: Coding = {
  code: '1865-5',
  display: 'Kivalina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kobuk: Coding = {
  code: '1866-3',
  display: 'Kobuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kotzebue: Coding = {
  code: '1867-1',
  display: 'Kotzebue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Koyuk: Coding = {
  code: '1868-9',
  display: 'Koyuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kwiguk: Coding = {
  code: '1869-7',
  display: 'Kwiguk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MaunelukInupiat: Coding = {
  code: '1870-5',
  display: 'Mauneluk Inupiat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NanaInupiat: Coding = {
  code: '1871-3',
  display: 'Nana Inupiat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Noatak: Coding = {
  code: '1872-1',
  display: 'Noatak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nome: Coding = {
  code: '1873-9',
  display: 'Nome',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Noorvik: Coding = {
  code: '1874-7',
  display: 'Noorvik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nuiqsut: Coding = {
  code: '1875-4',
  display: 'Nuiqsut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PointHope: Coding = {
  code: '1876-2',
  display: 'Point Hope',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PointLay: Coding = {
  code: '1877-0',
  display: 'Point Lay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Selawik: Coding = {
  code: '1878-8',
  display: 'Selawik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Shaktoolik: Coding = {
  code: '1879-6',
  display: 'Shaktoolik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Shishmaref: Coding = {
  code: '1880-4',
  display: 'Shishmaref',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Shungnak: Coding = {
  code: '1881-2',
  display: 'Shungnak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Solomon: Coding = {
  code: '1882-0',
  display: 'Solomon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Teller: Coding = {
  code: '1883-8',
  display: 'Teller',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Unalakleet: Coding = {
  code: '1884-6',
  display: 'Unalakleet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wainwright: Coding = {
  code: '1885-3',
  display: 'Wainwright',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Wales: Coding = {
  code: '1886-1',
  display: 'Wales',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WhiteMountain: Coding = {
  code: '1887-9',
  display: 'White Mountain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WhiteMountainInupiat: Coding = {
  code: '1888-7',
  display: 'White Mountain Inupiat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MaryQuoteSIgloo: Coding = {
  code: '1889-5',
  display: "Mary's Igloo",
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SiberianEskimo: Coding = {
  code: '1891-1',
  display: 'Siberian Eskimo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Gambell: Coding = {
  code: '1892-9',
  display: 'Gambell',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Savoonga: Coding = {
  code: '1893-7',
  display: 'Savoonga',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SiberianYupik: Coding = {
  code: '1894-5',
  display: 'Siberian Yupik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_YupikEskimo: Coding = {
  code: '1896-0',
  display: 'Yupik Eskimo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Akiachak: Coding = {
  code: '1897-8',
  display: 'Akiachak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Akiak: Coding = {
  code: '1898-6',
  display: 'Akiak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Alakanuk: Coding = {
  code: '1899-4',
  display: 'Alakanuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Aleknagik: Coding = {
  code: '1900-0',
  display: 'Aleknagik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Andreafsky: Coding = {
  code: '1901-8',
  display: 'Andreafsky',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Aniak: Coding = {
  code: '1902-6',
  display: 'Aniak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Atmautluak: Coding = {
  code: '1903-4',
  display: 'Atmautluak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Bethel: Coding = {
  code: '1904-2',
  display: 'Bethel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BillMooreQuoteSSlough: Coding = {
  code: '1905-9',
  display: "Bill Moore's Slough",
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BristolBayYupik: Coding = {
  code: '1906-7',
  display: 'Bristol Bay Yupik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CalistaYupik: Coding = {
  code: '1907-5',
  display: 'Calista Yupik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chefornak: Coding = {
  code: '1908-3',
  display: 'Chefornak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chevak: Coding = {
  code: '1909-1',
  display: 'Chevak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chuathbaluk: Coding = {
  code: '1910-9',
  display: 'Chuathbaluk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ClarkQuoteSPoint: Coding = {
  code: '1911-7',
  display: "Clark's Point",
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_CrookedCreek: Coding = {
  code: '1912-5',
  display: 'Crooked Creek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Dillingham: Coding = {
  code: '1913-3',
  display: 'Dillingham',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Eek: Coding = {
  code: '1914-1',
  display: 'Eek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ekuk: Coding = {
  code: '1915-8',
  display: 'Ekuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ekwok: Coding = {
  code: '1916-6',
  display: 'Ekwok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Emmonak: Coding = {
  code: '1917-4',
  display: 'Emmonak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_GoodnewsBay: Coding = {
  code: '1918-2',
  display: 'Goodnews Bay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_HooperBay: Coding = {
  code: '1919-0',
  display: 'Hooper Bay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_IqurmuitRussianMission: Coding = {
  code: '1920-8',
  display: 'Iqurmuit (Russian Mission)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kalskag: Coding = {
  code: '1921-6',
  display: 'Kalskag',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kasigluk: Coding = {
  code: '1922-4',
  display: 'Kasigluk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kipnuk: Coding = {
  code: '1923-2',
  display: 'Kipnuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Koliganek: Coding = {
  code: '1924-0',
  display: 'Koliganek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kongiganak: Coding = {
  code: '1925-7',
  display: 'Kongiganak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kotlik: Coding = {
  code: '1926-5',
  display: 'Kotlik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kwethluk: Coding = {
  code: '1927-3',
  display: 'Kwethluk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kwigillingok: Coding = {
  code: '1928-1',
  display: 'Kwigillingok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Levelock: Coding = {
  code: '1929-9',
  display: 'Levelock',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LowerKalskag: Coding = {
  code: '1930-7',
  display: 'Lower Kalskag',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Manokotak: Coding = {
  code: '1931-5',
  display: 'Manokotak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Marshall: Coding = {
  code: '1932-3',
  display: 'Marshall',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Mekoryuk: Coding = {
  code: '1933-1',
  display: 'Mekoryuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MountainVillage: Coding = {
  code: '1934-9',
  display: 'Mountain Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Naknek: Coding = {
  code: '1935-6',
  display: 'Naknek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Napaumute: Coding = {
  code: '1936-4',
  display: 'Napaumute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Napakiak: Coding = {
  code: '1937-2',
  display: 'Napakiak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Napaskiak: Coding = {
  code: '1938-0',
  display: 'Napaskiak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Newhalen: Coding = {
  code: '1939-8',
  display: 'Newhalen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NewStuyahok: Coding = {
  code: '1940-6',
  display: 'New Stuyahok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Newtok: Coding = {
  code: '1941-4',
  display: 'Newtok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nightmute: Coding = {
  code: '1942-2',
  display: 'Nightmute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nunapitchukv: Coding = {
  code: '1943-0',
  display: 'Nunapitchukv',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Oscarville: Coding = {
  code: '1944-8',
  display: 'Oscarville',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PilotStation: Coding = {
  code: '1945-5',
  display: 'Pilot Station',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PitkasPoint: Coding = {
  code: '1946-3',
  display: 'Pitkas Point',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Platinum: Coding = {
  code: '1947-1',
  display: 'Platinum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PortageCreek: Coding = {
  code: '1948-9',
  display: 'Portage Creek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Quinhagak: Coding = {
  code: '1949-7',
  display: 'Quinhagak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_RedDevil: Coding = {
  code: '1950-5',
  display: 'Red Devil',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_StMichael: Coding = {
  code: '1951-3',
  display: 'St. Michael',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ScammonBay: Coding = {
  code: '1952-1',
  display: 'Scammon Bay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SheldonQuoteSPoint: Coding = {
  code: '1953-9',
  display: "Sheldon's Point",
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Sleetmute: Coding = {
  code: '1954-7',
  display: 'Sleetmute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Stebbins: Coding = {
  code: '1955-4',
  display: 'Stebbins',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Togiak: Coding = {
  code: '1956-2',
  display: 'Togiak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Toksook: Coding = {
  code: '1957-0',
  display: 'Toksook',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tulukskak: Coding = {
  code: '1958-8',
  display: 'Tulukskak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tuntutuliak: Coding = {
  code: '1959-6',
  display: 'Tuntutuliak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tununak: Coding = {
  code: '1960-4',
  display: 'Tununak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_TwinHills: Coding = {
  code: '1961-2',
  display: 'Twin Hills',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Georgetown_2: Coding = {
  code: '1962-0',
  display: 'Georgetown',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_StMaryQuoteS: Coding = {
  code: '1963-8',
  display: "St. Mary's",
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Umkumiate: Coding = {
  code: '1964-6',
  display: 'Umkumiate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Aleut: Coding = {
  code: '1966-1',
  display: 'Aleut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AlutiiqAleut: Coding = {
  code: '1968-7',
  display: 'Alutiiq Aleut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tatitlek: Coding = {
  code: '1969-5',
  display: 'Tatitlek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ugashik: Coding = {
  code: '1970-3',
  display: 'Ugashik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_BristolBayAleut: Coding = {
  code: '1972-9',
  display: 'Bristol Bay Aleut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chignik: Coding = {
  code: '1973-7',
  display: 'Chignik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ChignikLake: Coding = {
  code: '1974-5',
  display: 'Chignik Lake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Egegik: Coding = {
  code: '1975-2',
  display: 'Egegik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Igiugig: Coding = {
  code: '1976-0',
  display: 'Igiugig',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_IvanofBay: Coding = {
  code: '1977-8',
  display: 'Ivanof Bay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_KingSalmon: Coding = {
  code: '1978-6',
  display: 'King Salmon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kokhanok: Coding = {
  code: '1979-4',
  display: 'Kokhanok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Perryville: Coding = {
  code: '1980-2',
  display: 'Perryville',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PilotPoint: Coding = {
  code: '1981-0',
  display: 'Pilot Point',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PortHeiden: Coding = {
  code: '1982-8',
  display: 'Port Heiden',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ChugachAleut: Coding = {
  code: '1984-4',
  display: 'Chugach Aleut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chenega: Coding = {
  code: '1985-1',
  display: 'Chenega',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ChugachCorporation: Coding = {
  code: '1986-9',
  display: 'Chugach Corporation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_EnglishBay: Coding = {
  code: '1987-7',
  display: 'English Bay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PortGraham: Coding = {
  code: '1988-5',
  display: 'Port Graham',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Eyak: Coding = {
  code: '1990-1',
  display: 'Eyak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_KoniagAleut: Coding = {
  code: '1992-7',
  display: 'Koniag Aleut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Akhiok: Coding = {
  code: '1993-5',
  display: 'Akhiok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Agdaagux: Coding = {
  code: '1994-3',
  display: 'Agdaagux',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Karluk: Coding = {
  code: '1995-0',
  display: 'Karluk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kodiak: Coding = {
  code: '1996-8',
  display: 'Kodiak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_LarsenBay: Coding = {
  code: '1997-6',
  display: 'Larsen Bay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OldHarbor: Coding = {
  code: '1998-4',
  display: 'Old Harbor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ouzinkie: Coding = {
  code: '1999-2',
  display: 'Ouzinkie',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PortLions: Coding = {
  code: '2000-8',
  display: 'Port Lions',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Sugpiaq: Coding = {
  code: '2002-4',
  display: 'Sugpiaq',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Suqpigaq: Coding = {
  code: '2004-0',
  display: 'Suqpigaq',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_UnanganAleut: Coding = {
  code: '2006-5',
  display: 'Unangan Aleut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Akutan: Coding = {
  code: '2007-3',
  display: 'Akutan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AleutCorporation: Coding = {
  code: '2008-1',
  display: 'Aleut Corporation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Aleutian: Coding = {
  code: '2009-9',
  display: 'Aleutian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AleutianIslander: Coding = {
  code: '2010-7',
  display: 'Aleutian Islander',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Atka: Coding = {
  code: '2011-5',
  display: 'Atka',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Belkofski: Coding = {
  code: '2012-3',
  display: 'Belkofski',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_ChignikLagoon: Coding = {
  code: '2013-1',
  display: 'Chignik Lagoon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_KingCove: Coding = {
  code: '2014-9',
  display: 'King Cove',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_FalsePass: Coding = {
  code: '2015-6',
  display: 'False Pass',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NelsonLagoon: Coding = {
  code: '2016-4',
  display: 'Nelson Lagoon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nikolski: Coding = {
  code: '2017-2',
  display: 'Nikolski',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PauloffHarbor: Coding = {
  code: '2018-0',
  display: 'Pauloff Harbor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_QaganToyagungin: Coding = {
  code: '2019-8',
  display: 'Qagan Toyagungin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Qawalangin: Coding = {
  code: '2020-6',
  display: 'Qawalangin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_StGeorge: Coding = {
  code: '2021-4',
  display: 'St. George',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_StPaul: Coding = {
  code: '2022-2',
  display: 'St. Paul',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SandPoint: Coding = {
  code: '2023-0',
  display: 'Sand Point',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SouthNaknek: Coding = {
  code: '2024-8',
  display: 'South Naknek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Unalaska: Coding = {
  code: '2025-5',
  display: 'Unalaska',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Unga: Coding = {
  code: '2026-3',
  display: 'Unga',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AsianIndian: Coding = {
  code: '2029-7',
  display: 'Asian Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Bangladeshi: Coding = {
  code: '2030-5',
  display: 'Bangladeshi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Bhutanese: Coding = {
  code: '2031-3',
  display: 'Bhutanese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Burmese: Coding = {
  code: '2032-1',
  display: 'Burmese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Cambodian: Coding = {
  code: '2033-9',
  display: 'Cambodian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chinese: Coding = {
  code: '2034-7',
  display: 'Chinese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Taiwanese: Coding = {
  code: '2035-4',
  display: 'Taiwanese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Filipino: Coding = {
  code: '2036-2',
  display: 'Filipino',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Hmong: Coding = {
  code: '2037-0',
  display: 'Hmong',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Indonesian: Coding = {
  code: '2038-8',
  display: 'Indonesian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Japanese: Coding = {
  code: '2039-6',
  display: 'Japanese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Korean: Coding = {
  code: '2040-4',
  display: 'Korean',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Laotian: Coding = {
  code: '2041-2',
  display: 'Laotian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Malaysian: Coding = {
  code: '2042-0',
  display: 'Malaysian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Okinawan: Coding = {
  code: '2043-8',
  display: 'Okinawan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Pakistani: Coding = {
  code: '2044-6',
  display: 'Pakistani',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SriLankan: Coding = {
  code: '2045-3',
  display: 'Sri Lankan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Thai: Coding = {
  code: '2046-1',
  display: 'Thai',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Vietnamese: Coding = {
  code: '2047-9',
  display: 'Vietnamese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_IwoJiman: Coding = {
  code: '2048-7',
  display: 'Iwo Jiman',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Maldivian: Coding = {
  code: '2049-5',
  display: 'Maldivian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nepalese: Coding = {
  code: '2050-3',
  display: 'Nepalese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Singaporean: Coding = {
  code: '2051-1',
  display: 'Singaporean',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Madagascar: Coding = {
  code: '2052-9',
  display: 'Madagascar',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Black: Coding = {
  code: '2056-0',
  display: 'Black',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_AfricanAmerican: Coding = {
  code: '2058-6',
  display: 'African American',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_African: Coding = {
  code: '2060-2',
  display: 'African',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Botswanan: Coding = {
  code: '2061-0',
  display: 'Botswanan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Ethiopian: Coding = {
  code: '2062-8',
  display: 'Ethiopian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Liberian: Coding = {
  code: '2063-6',
  display: 'Liberian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Namibian: Coding = {
  code: '2064-4',
  display: 'Namibian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Nigerian: Coding = {
  code: '2065-1',
  display: 'Nigerian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Zairean: Coding = {
  code: '2066-9',
  display: 'Zairean',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Bahamian: Coding = {
  code: '2067-7',
  display: 'Bahamian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Barbadian: Coding = {
  code: '2068-5',
  display: 'Barbadian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Dominican: Coding = {
  code: '2069-3',
  display: 'Dominican',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_DominicaIslander: Coding = {
  code: '2070-1',
  display: 'Dominica Islander',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Haitian: Coding = {
  code: '2071-9',
  display: 'Haitian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Jamaican: Coding = {
  code: '2072-7',
  display: 'Jamaican',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tobagoan: Coding = {
  code: '2073-5',
  display: 'Tobagoan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Trinidadian: Coding = {
  code: '2074-3',
  display: 'Trinidadian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_WestIndian: Coding = {
  code: '2075-0',
  display: 'West Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Polynesian: Coding = {
  code: '2078-4',
  display: 'Polynesian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NativeHawaiian: Coding = {
  code: '2079-2',
  display: 'Native Hawaiian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Samoan: Coding = {
  code: '2080-0',
  display: 'Samoan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tahitian: Coding = {
  code: '2081-8',
  display: 'Tahitian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tongan: Coding = {
  code: '2082-6',
  display: 'Tongan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Tokelauan: Coding = {
  code: '2083-4',
  display: 'Tokelauan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Micronesian: Coding = {
  code: '2085-9',
  display: 'Micronesian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_GuamanianOrChamorro: Coding = {
  code: '2086-7',
  display: 'Guamanian or Chamorro',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Guamanian: Coding = {
  code: '2087-5',
  display: 'Guamanian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chamorro: Coding = {
  code: '2088-3',
  display: 'Chamorro',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MarianaIslander: Coding = {
  code: '2089-1',
  display: 'Mariana Islander',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Marshallese: Coding = {
  code: '2090-9',
  display: 'Marshallese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Palauan: Coding = {
  code: '2091-7',
  display: 'Palauan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Carolinian: Coding = {
  code: '2092-5',
  display: 'Carolinian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kosraean: Coding = {
  code: '2093-3',
  display: 'Kosraean',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Pohnpeian: Coding = {
  code: '2094-1',
  display: 'Pohnpeian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Saipanese: Coding = {
  code: '2095-8',
  display: 'Saipanese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Kiribati: Coding = {
  code: '2096-6',
  display: 'Kiribati',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Chuukese: Coding = {
  code: '2097-4',
  display: 'Chuukese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Yapese: Coding = {
  code: '2098-2',
  display: 'Yapese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Melanesian: Coding = {
  code: '2100-6',
  display: 'Melanesian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Fijian: Coding = {
  code: '2101-4',
  display: 'Fijian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_PapuaNewGuinean: Coding = {
  code: '2102-2',
  display: 'Papua New Guinean',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_SolomonIslander: Coding = {
  code: '2103-0',
  display: 'Solomon Islander',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_NewHebrides: Coding = {
  code: '2104-8',
  display: 'New Hebrides',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_European: Coding = {
  code: '2108-9',
  display: 'European',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Armenian: Coding = {
  code: '2109-7',
  display: 'Armenian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_English: Coding = {
  code: '2110-5',
  display: 'English',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_French: Coding = {
  code: '2111-3',
  display: 'French',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_German: Coding = {
  code: '2112-1',
  display: 'German',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Irish: Coding = {
  code: '2113-9',
  display: 'Irish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Italian: Coding = {
  code: '2114-7',
  display: 'Italian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Polish: Coding = {
  code: '2115-4',
  display: 'Polish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Scottish: Coding = {
  code: '2116-2',
  display: 'Scottish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_MiddleEasternOrNorthAfrican: Coding = {
  code: '2118-8',
  display: 'Middle Eastern or North African',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Assyrian: Coding = {
  code: '2119-6',
  display: 'Assyrian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Egyptian: Coding = {
  code: '2120-4',
  display: 'Egyptian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Iranian: Coding = {
  code: '2121-2',
  display: 'Iranian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Iraqi: Coding = {
  code: '2122-0',
  display: 'Iraqi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Lebanese: Coding = {
  code: '2123-8',
  display: 'Lebanese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Palestinian: Coding = {
  code: '2124-6',
  display: 'Palestinian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Syrian: Coding = {
  code: '2125-3',
  display: 'Syrian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Afghanistani: Coding = {
  code: '2126-1',
  display: 'Afghanistani',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Israeili: Coding = {
  code: '2127-9',
  display: 'Israeili',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_Arab: Coding = {
  code: '2129-5',
  display: 'Arab',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
const v3_Race_OtherPacificIslander: Coding = {
  code: '2500-7',
  display: 'Other Pacific Islander',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Race',
};
/**
 *  In the United States, federal standards for classifying data on race determine the categories used by federal agencies and exert a strong influence on categorization by state and local agencies and private sector organizations.  The federal standards do not conceptually define race, and they recognize the absence of an anthropological or scientific basis for racial classification.  Instead, the federal standards acknowledge that race is a social-political construct in which an individual's own identification with one more race categories is preferred to observer identification. The standards use a variety of features to define five minimum race categories. Among these features are descent from "the original peoples" of a specified region or nation.  The minimum race categories are American Indian or Alaska Native, Asian, Black or African American, Native Hawaiian or Other Pacific Islander, and White.  The federal standards stipulate that race data need not be limited to the five minimum categories, but any expansion must be collapsible to those categories.
 */
export const V3Race = {
  /**
   * American Indian or Alaska Native
   */
  AmericanIndianOrAlaskaNative: v3_Race_AmericanIndianOrAlaskaNative,
  /**
   * American Indian
   */
  AmericanIndian: v3_Race_AmericanIndian,
  /**
   * Abenaki
   */
  Abenaki: v3_Race_Abenaki,
  /**
   * Algonquian
   */
  Algonquian: v3_Race_Algonquian,
  /**
   * Apache
   */
  Apache: v3_Race_Apache,
  /**
   * Chiricahua
   */
  Chiricahua: v3_Race_Chiricahua,
  /**
   * Fort Sill Apache
   */
  FortSillApache: v3_Race_FortSillApache,
  /**
   * Jicarilla Apache
   */
  JicarillaApache: v3_Race_JicarillaApache,
  /**
   * Lipan Apache
   */
  LipanApache: v3_Race_LipanApache,
  /**
   * Mescalero Apache
   */
  MescaleroApache: v3_Race_MescaleroApache,
  /**
   * Oklahoma Apache
   */
  OklahomaApache: v3_Race_OklahomaApache,
  /**
   * Payson Apache
   */
  PaysonApache: v3_Race_PaysonApache,
  /**
   * San Carlos Apache
   */
  SanCarlosApache: v3_Race_SanCarlosApache,
  /**
   * White Mountain Apache
   */
  WhiteMountainApache: v3_Race_WhiteMountainApache,
  /**
   * Arapaho
   */
  Arapaho: v3_Race_Arapaho,
  /**
   * Northern Arapaho
   */
  NorthernArapaho: v3_Race_NorthernArapaho,
  /**
   * Southern Arapaho
   */
  SouthernArapaho: v3_Race_SouthernArapaho,
  /**
   * Wind River Arapaho
   */
  WindRiverArapaho: v3_Race_WindRiverArapaho,
  /**
   * Arikara
   */
  Arikara: v3_Race_Arikara,
  /**
   * Assiniboine
   */
  Assiniboine: v3_Race_Assiniboine,
  /**
   * Assiniboine Sioux
   */
  AssiniboineSioux: v3_Race_AssiniboineSioux,
  /**
   * Fort Peck Assiniboine Sioux
   */
  FortPeckAssiniboineSioux: v3_Race_FortPeckAssiniboineSioux,
  /**
   * Bannock
   */
  Bannock: v3_Race_Bannock,
  /**
   * Blackfeet
   */
  Blackfeet: v3_Race_Blackfeet,
  /**
   * Brotherton
   */
  Brotherton: v3_Race_Brotherton,
  /**
   * Burt Lake Band
   */
  BurtLakeBand: v3_Race_BurtLakeBand,
  /**
   * Caddo
   */
  Caddo: v3_Race_Caddo,
  /**
   * Oklahoma Cado
   */
  OklahomaCado: v3_Race_OklahomaCado,
  /**
   * Cahuilla
   */
  Cahuilla: v3_Race_Cahuilla,
  /**
   * Agua Caliente Cahuilla
   */
  AguaCalienteCahuilla: v3_Race_AguaCalienteCahuilla,
  /**
   * Augustine
   */
  Augustine: v3_Race_Augustine,
  /**
   * Cabazon
   */
  Cabazon: v3_Race_Cabazon,
  /**
   * Los Coyotes
   */
  LosCoyotes: v3_Race_LosCoyotes,
  /**
   * Morongo
   */
  Morongo: v3_Race_Morongo,
  /**
   * Santa Rosa Cahuilla
   */
  SantaRosaCahuilla: v3_Race_SantaRosaCahuilla,
  /**
   * Torres-Martinez
   */
  TorresMartinez: v3_Race_TorresMartinez,
  /**
   * California Tribes
   */
  CaliforniaTribes: v3_Race_CaliforniaTribes,
  /**
   * Cahto
   */
  Cahto: v3_Race_Cahto,
  /**
   * Chimariko
   */
  Chimariko: v3_Race_Chimariko,
  /**
   * Coast Miwok
   */
  CoastMiwok: v3_Race_CoastMiwok,
  /**
   * Digger
   */
  Digger: v3_Race_Digger,
  /**
   * Kawaiisu
   */
  Kawaiisu: v3_Race_Kawaiisu,
  /**
   * Kern River
   */
  KernRiver: v3_Race_KernRiver,
  /**
   * Mattole
   */
  Mattole: v3_Race_Mattole,
  /**
   * Red Wood
   */
  RedWood: v3_Race_RedWood,
  /**
   * Santa Rosa
   */
  SantaRosa: v3_Race_SantaRosa,
  /**
   * Takelma
   */
  Takelma: v3_Race_Takelma,
  /**
   * Wappo
   */
  Wappo: v3_Race_Wappo,
  /**
   * Yana
   */
  Yana: v3_Race_Yana,
  /**
   * Yuki
   */
  Yuki: v3_Race_Yuki,
  /**
   * Canadian and Latin American Indian
   */
  CanadianAndLatinAmericanIndian: v3_Race_CanadianAndLatinAmericanIndian,
  /**
   * Canadian Indian
   */
  CanadianIndian: v3_Race_CanadianIndian,
  /**
   * Central American Indian
   */
  CentralAmericanIndian: v3_Race_CentralAmericanIndian,
  /**
   * French American Indian
   */
  FrenchAmericanIndian: v3_Race_FrenchAmericanIndian,
  /**
   * Mexican American Indian
   */
  MexicanAmericanIndian: v3_Race_MexicanAmericanIndian,
  /**
   * South American Indian
   */
  SouthAmericanIndian: v3_Race_SouthAmericanIndian,
  /**
   * Spanish American Indian
   */
  SpanishAmericanIndian: v3_Race_SpanishAmericanIndian,
  /**
   * Catawba
   */
  Catawba: v3_Race_Catawba,
  /**
   * Cayuse
   */
  Cayuse: v3_Race_Cayuse,
  /**
   * Chehalis
   */
  Chehalis: v3_Race_Chehalis,
  /**
   * Chemakuan
   */
  Chemakuan: v3_Race_Chemakuan,
  /**
   * Hoh
   */
  Hoh: v3_Race_Hoh,
  /**
   * Quileute
   */
  Quileute: v3_Race_Quileute,
  /**
   * Chemehuevi
   */
  Chemehuevi: v3_Race_Chemehuevi,
  /**
   * Cherokee
   */
  Cherokee: v3_Race_Cherokee,
  /**
   * Cherokee Alabama
   */
  CherokeeAlabama: v3_Race_CherokeeAlabama,
  /**
   * Cherokees of Northeast Alabama
   */
  CherokeesOfNortheastAlabama: v3_Race_CherokeesOfNortheastAlabama,
  /**
   * Cherokees of Southeast Alabama
   */
  CherokeesOfSoutheastAlabama: v3_Race_CherokeesOfSoutheastAlabama,
  /**
   * Eastern Cherokee
   */
  EasternCherokee: v3_Race_EasternCherokee,
  /**
   * Echota Cherokee
   */
  EchotaCherokee: v3_Race_EchotaCherokee,
  /**
   * Etowah Cherokee
   */
  EtowahCherokee: v3_Race_EtowahCherokee,
  /**
   * Northern Cherokee
   */
  NorthernCherokee: v3_Race_NorthernCherokee,
  /**
   * Tuscola
   */
  Tuscola: v3_Race_Tuscola,
  /**
   * United Keetowah Band of Cherokee
   */
  UnitedKeetowahBandOfCherokee: v3_Race_UnitedKeetowahBandOfCherokee,
  /**
   * Western Cherokee
   */
  WesternCherokee: v3_Race_WesternCherokee,
  /**
   * Cherokee Shawnee
   */
  CherokeeShawnee: v3_Race_CherokeeShawnee,
  /**
   * Cheyenne
   */
  Cheyenne: v3_Race_Cheyenne,
  /**
   * Northern Cheyenne
   */
  NorthernCheyenne: v3_Race_NorthernCheyenne,
  /**
   * Southern Cheyenne
   */
  SouthernCheyenne: v3_Race_SouthernCheyenne,
  /**
   * Cheyenne-Arapaho
   */
  CheyenneArapaho: v3_Race_CheyenneArapaho,
  /**
   * Chickahominy
   */
  Chickahominy: v3_Race_Chickahominy,
  /**
   * Eastern Chickahominy
   */
  EasternChickahominy: v3_Race_EasternChickahominy,
  /**
   * Western Chickahominy
   */
  WesternChickahominy: v3_Race_WesternChickahominy,
  /**
   * Chickasaw
   */
  Chickasaw: v3_Race_Chickasaw,
  /**
   * Chinook
   */
  Chinook: v3_Race_Chinook,
  /**
   * Clatsop
   */
  Clatsop: v3_Race_Clatsop,
  /**
   * Columbia River Chinook
   */
  ColumbiaRiverChinook: v3_Race_ColumbiaRiverChinook,
  /**
   * Kathlamet
   */
  Kathlamet: v3_Race_Kathlamet,
  /**
   * Upper Chinook
   */
  UpperChinook: v3_Race_UpperChinook,
  /**
   * Wakiakum Chinook
   */
  WakiakumChinook: v3_Race_WakiakumChinook,
  /**
   * Willapa Chinook
   */
  WillapaChinook: v3_Race_WillapaChinook,
  /**
   * Wishram
   */
  Wishram: v3_Race_Wishram,
  /**
   * Chippewa
   */
  Chippewa: v3_Race_Chippewa,
  /**
   * Bad River
   */
  BadRiver: v3_Race_BadRiver,
  /**
   * Bay Mills Chippewa
   */
  BayMillsChippewa: v3_Race_BayMillsChippewa,
  /**
   * Bois Forte
   */
  BoisForte: v3_Race_BoisForte,
  /**
   * Burt Lake Chippewa
   */
  BurtLakeChippewa: v3_Race_BurtLakeChippewa,
  /**
   * Fond du Lac
   */
  FondDuLac: v3_Race_FondDuLac,
  /**
   * Grand Portage
   */
  GrandPortage: v3_Race_GrandPortage,
  /**
   * Grand Traverse Band of Ottawa-Chippewa
   */
  GrandTraverseBandOfOttawaChippewa: v3_Race_GrandTraverseBandOfOttawaChippewa,
  /**
   * Keweenaw
   */
  Keweenaw: v3_Race_Keweenaw,
  /**
   * Lac Courte Oreilles
   */
  LacCourteOreilles: v3_Race_LacCourteOreilles,
  /**
   * Lac du Flambeau
   */
  LacDuFlambeau: v3_Race_LacDuFlambeau,
  /**
   * Lac Vieux Desert Chippewa
   */
  LacVieuxDesertChippewa: v3_Race_LacVieuxDesertChippewa,
  /**
   * Lake Superior
   */
  LakeSuperior: v3_Race_LakeSuperior,
  /**
   * Leech Lake
   */
  LeechLake: v3_Race_LeechLake,
  /**
   * Little Shell Chippewa
   */
  LittleShellChippewa: v3_Race_LittleShellChippewa,
  /**
   * Mille Lacs
   */
  MilleLacs: v3_Race_MilleLacs,
  /**
   * Minnesota Chippewa
   */
  MinnesotaChippewa: v3_Race_MinnesotaChippewa,
  /**
   * Ontonagon
   */
  Ontonagon: v3_Race_Ontonagon,
  /**
   * Red Cliff Chippewa
   */
  RedCliffChippewa: v3_Race_RedCliffChippewa,
  /**
   * Red Lake Chippewa
   */
  RedLakeChippewa: v3_Race_RedLakeChippewa,
  /**
   * Saginaw Chippewa
   */
  SaginawChippewa: v3_Race_SaginawChippewa,
  /**
   * St. Croix Chippewa
   */
  StCroixChippewa: v3_Race_StCroixChippewa,
  /**
   * Sault Ste. Marie Chippewa
   */
  SaultSteMarieChippewa: v3_Race_SaultSteMarieChippewa,
  /**
   * Sokoagon Chippewa
   */
  SokoagonChippewa: v3_Race_SokoagonChippewa,
  /**
   * Turtle Mountain
   */
  TurtleMountain: v3_Race_TurtleMountain,
  /**
   * White Earth
   */
  WhiteEarth: v3_Race_WhiteEarth,
  /**
   * Chippewa Cree
   */
  ChippewaCree: v3_Race_ChippewaCree,
  /**
   * Rocky Boy's Chippewa Cree
   */
  RockyBoyQuoteSChippewaCree: v3_Race_RockyBoyQuoteSChippewaCree,
  /**
   * Chitimacha
   */
  Chitimacha: v3_Race_Chitimacha,
  /**
   * Choctaw
   */
  Choctaw: v3_Race_Choctaw,
  /**
   * Clifton Choctaw
   */
  CliftonChoctaw: v3_Race_CliftonChoctaw,
  /**
   * Jena Choctaw
   */
  JenaChoctaw: v3_Race_JenaChoctaw,
  /**
   * Mississippi Choctaw
   */
  MississippiChoctaw: v3_Race_MississippiChoctaw,
  /**
   * Mowa Band of Choctaw
   */
  MowaBandOfChoctaw: v3_Race_MowaBandOfChoctaw,
  /**
   * Oklahoma Choctaw
   */
  OklahomaChoctaw: v3_Race_OklahomaChoctaw,
  /**
   * Chumash
   */
  Chumash: v3_Race_Chumash,
  /**
   * Santa Ynez
   */
  SantaYnez: v3_Race_SantaYnez,
  /**
   * Clear Lake
   */
  ClearLake: v3_Race_ClearLake,
  /**
   * Coeur D'Alene
   */
  CoeurDQuoteAlene: v3_Race_CoeurDQuoteAlene,
  /**
   * Coharie
   */
  Coharie: v3_Race_Coharie,
  /**
   * Colorado River
   */
  ColoradoRiver: v3_Race_ColoradoRiver,
  /**
   * Colville
   */
  Colville: v3_Race_Colville,
  /**
   * Comanche
   */
  Comanche: v3_Race_Comanche,
  /**
   * Oklahoma Comanche
   */
  OklahomaComanche: v3_Race_OklahomaComanche,
  /**
   * Coos, Lower Umpqua, Siuslaw
   */
  CoosLowerUmpquaSiuslaw: v3_Race_CoosLowerUmpquaSiuslaw,
  /**
   * Coos
   */
  Coos: v3_Race_Coos,
  /**
   * Coquilles
   */
  Coquilles: v3_Race_Coquilles,
  /**
   * Costanoan
   */
  Costanoan: v3_Race_Costanoan,
  /**
   * Coushatta
   */
  Coushatta: v3_Race_Coushatta,
  /**
   * Alabama Coushatta
   */
  AlabamaCoushatta: v3_Race_AlabamaCoushatta,
  /**
   * Cowlitz
   */
  Cowlitz: v3_Race_Cowlitz,
  /**
   * Cree
   */
  Cree: v3_Race_Cree,
  /**
   * Creek
   */
  Creek: v3_Race_Creek,
  /**
   * Alabama Creek
   */
  AlabamaCreek: v3_Race_AlabamaCreek,
  /**
   * Alabama Quassarte
   */
  AlabamaQuassarte: v3_Race_AlabamaQuassarte,
  /**
   * Eastern Creek
   */
  EasternCreek: v3_Race_EasternCreek,
  /**
   * Eastern Muscogee
   */
  EasternMuscogee: v3_Race_EasternMuscogee,
  /**
   * Kialegee
   */
  Kialegee: v3_Race_Kialegee,
  /**
   * Lower Muscogee
   */
  LowerMuscogee: v3_Race_LowerMuscogee,
  /**
   * Machis Lower Creek Indian
   */
  MachisLowerCreekIndian: v3_Race_MachisLowerCreekIndian,
  /**
   * Poarch Band
   */
  PoarchBand: v3_Race_PoarchBand,
  /**
   * Principal Creek Indian Nation
   */
  PrincipalCreekIndianNation: v3_Race_PrincipalCreekIndianNation,
  /**
   * Star Clan of Muscogee Creeks
   */
  StarClanOfMuscogeeCreeks: v3_Race_StarClanOfMuscogeeCreeks,
  /**
   * Thlopthlocco
   */
  Thlopthlocco: v3_Race_Thlopthlocco,
  /**
   * Tuckabachee
   */
  Tuckabachee: v3_Race_Tuckabachee,
  /**
   * Croatan
   */
  Croatan: v3_Race_Croatan,
  /**
   * Crow
   */
  Crow: v3_Race_Crow,
  /**
   * Cupeno
   */
  Cupeno: v3_Race_Cupeno,
  /**
   * Agua Caliente
   */
  AguaCaliente: v3_Race_AguaCaliente,
  /**
   * Delaware
   */
  Delaware: v3_Race_Delaware,
  /**
   * Eastern Delaware
   */
  EasternDelaware: v3_Race_EasternDelaware,
  /**
   * Lenni-Lenape
   */
  LenniLenape: v3_Race_LenniLenape,
  /**
   * Munsee
   */
  Munsee: v3_Race_Munsee,
  /**
   * Oklahoma Delaware
   */
  OklahomaDelaware: v3_Race_OklahomaDelaware,
  /**
   * Rampough Mountain
   */
  RampoughMountain: v3_Race_RampoughMountain,
  /**
   * Sand Hill
   */
  SandHill: v3_Race_SandHill,
  /**
   * Diegueno
   */
  Diegueno: v3_Race_Diegueno,
  /**
   * Campo
   */
  Campo: v3_Race_Campo,
  /**
   * Capitan Grande
   */
  CapitanGrande: v3_Race_CapitanGrande,
  /**
   * Cuyapaipe
   */
  Cuyapaipe: v3_Race_Cuyapaipe,
  /**
   * La Posta
   */
  LaPosta: v3_Race_LaPosta,
  /**
   * Manzanita
   */
  Manzanita: v3_Race_Manzanita,
  /**
   * Mesa Grande
   */
  MesaGrande: v3_Race_MesaGrande,
  /**
   * San Pasqual
   */
  SanPasqual: v3_Race_SanPasqual,
  /**
   * Santa Ysabel
   */
  SantaYsabel: v3_Race_SantaYsabel,
  /**
   * Sycuan
   */
  Sycuan: v3_Race_Sycuan,
  /**
   * Eastern Tribes
   */
  EasternTribes: v3_Race_EasternTribes,
  /**
   * Attacapa
   */
  Attacapa: v3_Race_Attacapa,
  /**
   * Biloxi
   */
  Biloxi: v3_Race_Biloxi,
  /**
   * Georgetown
   */
  Georgetown: v3_Race_Georgetown,
  /**
   * Moor
   */
  Moor: v3_Race_Moor,
  /**
   * Nansemond
   */
  Nansemond: v3_Race_Nansemond,
  /**
   * Natchez
   */
  Natchez: v3_Race_Natchez,
  /**
   * Nausu Waiwash
   */
  NausuWaiwash: v3_Race_NausuWaiwash,
  /**
   * Nipmuc
   */
  Nipmuc: v3_Race_Nipmuc,
  /**
   * Paugussett
   */
  Paugussett: v3_Race_Paugussett,
  /**
   * Pocomoke Acohonock
   */
  PocomokeAcohonock: v3_Race_PocomokeAcohonock,
  /**
   * Southeastern Indians
   */
  SoutheasternIndians: v3_Race_SoutheasternIndians,
  /**
   * Susquehanock
   */
  Susquehanock: v3_Race_Susquehanock,
  /**
   * Tunica Biloxi
   */
  TunicaBiloxi: v3_Race_TunicaBiloxi,
  /**
   * Waccamaw-Siousan
   */
  WaccamawSiousan: v3_Race_WaccamawSiousan,
  /**
   * Wicomico
   */
  Wicomico: v3_Race_Wicomico,
  /**
   * Esselen
   */
  Esselen: v3_Race_Esselen,
  /**
   * Fort Belknap
   */
  FortBelknap: v3_Race_FortBelknap,
  /**
   * Fort Berthold
   */
  FortBerthold: v3_Race_FortBerthold,
  /**
   * Fort Mcdowell
   */
  FortMcdowell: v3_Race_FortMcdowell,
  /**
   * Fort Hall
   */
  FortHall: v3_Race_FortHall,
  /**
   * Gabrieleno
   */
  Gabrieleno: v3_Race_Gabrieleno,
  /**
   * Grand Ronde
   */
  GrandRonde: v3_Race_GrandRonde,
  /**
   * Gros Ventres
   */
  GrosVentres: v3_Race_GrosVentres,
  /**
   * Atsina
   */
  Atsina: v3_Race_Atsina,
  /**
   * Haliwa
   */
  Haliwa: v3_Race_Haliwa,
  /**
   * Hidatsa
   */
  Hidatsa: v3_Race_Hidatsa,
  /**
   * Hoopa
   */
  Hoopa: v3_Race_Hoopa,
  /**
   * Trinity
   */
  Trinity: v3_Race_Trinity,
  /**
   * Whilkut
   */
  Whilkut: v3_Race_Whilkut,
  /**
   * Hoopa Extension
   */
  HoopaExtension: v3_Race_HoopaExtension,
  /**
   * Houma
   */
  Houma: v3_Race_Houma,
  /**
   * Inaja-Cosmit
   */
  InajaCosmit: v3_Race_InajaCosmit,
  /**
   * Iowa
   */
  Iowa: v3_Race_Iowa,
  /**
   * Iowa of Kansas-Nebraska
   */
  IowaOfKansasNebraska: v3_Race_IowaOfKansasNebraska,
  /**
   * Iowa of Oklahoma
   */
  IowaOfOklahoma: v3_Race_IowaOfOklahoma,
  /**
   * Iroquois
   */
  Iroquois: v3_Race_Iroquois,
  /**
   * Cayuga
   */
  Cayuga: v3_Race_Cayuga,
  /**
   * Mohawk
   */
  Mohawk: v3_Race_Mohawk,
  /**
   * Oneida
   */
  Oneida: v3_Race_Oneida,
  /**
   * Onondaga
   */
  Onondaga: v3_Race_Onondaga,
  /**
   * Seneca
   */
  Seneca: v3_Race_Seneca,
  /**
   * Seneca Nation
   */
  SenecaNation: v3_Race_SenecaNation,
  /**
   * Seneca-Cayuga
   */
  SenecaCayuga: v3_Race_SenecaCayuga,
  /**
   * Tonawanda Seneca
   */
  TonawandaSeneca: v3_Race_TonawandaSeneca,
  /**
   * Tuscarora
   */
  Tuscarora: v3_Race_Tuscarora,
  /**
   * Wyandotte
   */
  Wyandotte: v3_Race_Wyandotte,
  /**
   * Juaneno
   */
  Juaneno: v3_Race_Juaneno,
  /**
   * Kalispel
   */
  Kalispel: v3_Race_Kalispel,
  /**
   * Karuk
   */
  Karuk: v3_Race_Karuk,
  /**
   * Kaw
   */
  Kaw: v3_Race_Kaw,
  /**
   * Kickapoo
   */
  Kickapoo: v3_Race_Kickapoo,
  /**
   * Oklahoma Kickapoo
   */
  OklahomaKickapoo: v3_Race_OklahomaKickapoo,
  /**
   * Texas Kickapoo
   */
  TexasKickapoo: v3_Race_TexasKickapoo,
  /**
   * Kiowa
   */
  Kiowa: v3_Race_Kiowa,
  /**
   * Oklahoma Kiowa
   */
  OklahomaKiowa: v3_Race_OklahomaKiowa,
  /**
   * Klallam
   */
  Klallam: v3_Race_Klallam,
  /**
   * Jamestown
   */
  Jamestown: v3_Race_Jamestown,
  /**
   * Lower Elwha
   */
  LowerElwha: v3_Race_LowerElwha,
  /**
   * Port Gamble Klallam
   */
  PortGambleKlallam: v3_Race_PortGambleKlallam,
  /**
   * Klamath
   */
  Klamath: v3_Race_Klamath,
  /**
   * Konkow
   */
  Konkow: v3_Race_Konkow,
  /**
   * Kootenai
   */
  Kootenai: v3_Race_Kootenai,
  /**
   * Lassik
   */
  Lassik: v3_Race_Lassik,
  /**
   * Long Island
   */
  LongIsland: v3_Race_LongIsland,
  /**
   * Matinecock
   */
  Matinecock: v3_Race_Matinecock,
  /**
   * Montauk
   */
  Montauk: v3_Race_Montauk,
  /**
   * Poospatuck
   */
  Poospatuck: v3_Race_Poospatuck,
  /**
   * Setauket
   */
  Setauket: v3_Race_Setauket,
  /**
   * Luiseno
   */
  Luiseno: v3_Race_Luiseno,
  /**
   * La Jolla
   */
  LaJolla: v3_Race_LaJolla,
  /**
   * Pala
   */
  Pala: v3_Race_Pala,
  /**
   * Pauma
   */
  Pauma: v3_Race_Pauma,
  /**
   * Pechanga
   */
  Pechanga: v3_Race_Pechanga,
  /**
   * Soboba
   */
  Soboba: v3_Race_Soboba,
  /**
   * Twenty-Nine Palms
   */
  TwentyNinePalms: v3_Race_TwentyNinePalms,
  /**
   * Temecula
   */
  Temecula: v3_Race_Temecula,
  /**
   * Lumbee
   */
  Lumbee: v3_Race_Lumbee,
  /**
   * Lummi
   */
  Lummi: v3_Race_Lummi,
  /**
   * Maidu
   */
  Maidu: v3_Race_Maidu,
  /**
   * Mountain Maidu
   */
  MountainMaidu: v3_Race_MountainMaidu,
  /**
   * Nishinam
   */
  Nishinam: v3_Race_Nishinam,
  /**
   * Makah
   */
  Makah: v3_Race_Makah,
  /**
   * Maliseet
   */
  Maliseet: v3_Race_Maliseet,
  /**
   * Mandan
   */
  Mandan: v3_Race_Mandan,
  /**
   * Mattaponi
   */
  Mattaponi: v3_Race_Mattaponi,
  /**
   * Menominee
   */
  Menominee: v3_Race_Menominee,
  /**
   * Miami
   */
  Miami: v3_Race_Miami,
  /**
   * Illinois Miami
   */
  IllinoisMiami: v3_Race_IllinoisMiami,
  /**
   * Indiana Miami
   */
  IndianaMiami: v3_Race_IndianaMiami,
  /**
   * Oklahoma Miami
   */
  OklahomaMiami: v3_Race_OklahomaMiami,
  /**
   * Miccosukee
   */
  Miccosukee: v3_Race_Miccosukee,
  /**
   * Micmac
   */
  Micmac: v3_Race_Micmac,
  /**
   * Aroostook
   */
  Aroostook: v3_Race_Aroostook,
  /**
   * Mission Indians
   */
  MissionIndians: v3_Race_MissionIndians,
  /**
   * Miwok
   */
  Miwok: v3_Race_Miwok,
  /**
   * Modoc
   */
  Modoc: v3_Race_Modoc,
  /**
   * Mohegan
   */
  Mohegan: v3_Race_Mohegan,
  /**
   * Mono
   */
  Mono: v3_Race_Mono,
  /**
   * Nanticoke
   */
  Nanticoke: v3_Race_Nanticoke,
  /**
   * Narragansett
   */
  Narragansett: v3_Race_Narragansett,
  /**
   * Navajo
   */
  Navajo: v3_Race_Navajo,
  /**
   * Alamo Navajo
   */
  AlamoNavajo: v3_Race_AlamoNavajo,
  /**
   * Canoncito Navajo
   */
  CanoncitoNavajo: v3_Race_CanoncitoNavajo,
  /**
   * Ramah Navajo
   */
  RamahNavajo: v3_Race_RamahNavajo,
  /**
   * Nez Perce
   */
  NezPerce: v3_Race_NezPerce,
  /**
   * Nomalaki
   */
  Nomalaki: v3_Race_Nomalaki,
  /**
   * Northwest Tribes
   */
  NorthwestTribes: v3_Race_NorthwestTribes,
  /**
   * Alsea
   */
  Alsea: v3_Race_Alsea,
  /**
   * Celilo
   */
  Celilo: v3_Race_Celilo,
  /**
   * Columbia
   */
  Columbia: v3_Race_Columbia,
  /**
   * Kalapuya
   */
  Kalapuya: v3_Race_Kalapuya,
  /**
   * Molala
   */
  Molala: v3_Race_Molala,
  /**
   * Talakamish
   */
  Talakamish: v3_Race_Talakamish,
  /**
   * Tenino
   */
  Tenino: v3_Race_Tenino,
  /**
   * Tillamook
   */
  Tillamook: v3_Race_Tillamook,
  /**
   * Wenatchee
   */
  Wenatchee: v3_Race_Wenatchee,
  /**
   * Yahooskin
   */
  Yahooskin: v3_Race_Yahooskin,
  /**
   * Omaha
   */
  Omaha: v3_Race_Omaha,
  /**
   * Oregon Athabaskan
   */
  OregonAthabaskan: v3_Race_OregonAthabaskan,
  /**
   * Osage
   */
  Osage: v3_Race_Osage,
  /**
   * Otoe-Missouria
   */
  OtoeMissouria: v3_Race_OtoeMissouria,
  /**
   * Ottawa
   */
  Ottawa: v3_Race_Ottawa,
  /**
   * Burt Lake Ottawa
   */
  BurtLakeOttawa: v3_Race_BurtLakeOttawa,
  /**
   * Michigan Ottawa
   */
  MichiganOttawa: v3_Race_MichiganOttawa,
  /**
   * Oklahoma Ottawa
   */
  OklahomaOttawa: v3_Race_OklahomaOttawa,
  /**
   * Paiute
   */
  Paiute: v3_Race_Paiute,
  /**
   * Bishop
   */
  Bishop: v3_Race_Bishop,
  /**
   * Bridgeport
   */
  Bridgeport: v3_Race_Bridgeport,
  /**
   * Burns Paiute
   */
  BurnsPaiute: v3_Race_BurnsPaiute,
  /**
   * Cedarville
   */
  Cedarville: v3_Race_Cedarville,
  /**
   * Fort Bidwell
   */
  FortBidwell: v3_Race_FortBidwell,
  /**
   * Fort Independence
   */
  FortIndependence: v3_Race_FortIndependence,
  /**
   * Kaibab
   */
  Kaibab: v3_Race_Kaibab,
  /**
   * Las Vegas
   */
  LasVegas: v3_Race_LasVegas,
  /**
   * Lone Pine
   */
  LonePine: v3_Race_LonePine,
  /**
   * Lovelock
   */
  Lovelock: v3_Race_Lovelock,
  /**
   * Malheur Paiute
   */
  MalheurPaiute: v3_Race_MalheurPaiute,
  /**
   * Moapa
   */
  Moapa: v3_Race_Moapa,
  /**
   * Northern Paiute
   */
  NorthernPaiute: v3_Race_NorthernPaiute,
  /**
   * Owens Valley
   */
  OwensValley: v3_Race_OwensValley,
  /**
   * Pyramid Lake
   */
  PyramidLake: v3_Race_PyramidLake,
  /**
   * San Juan Southern Paiute
   */
  SanJuanSouthernPaiute: v3_Race_SanJuanSouthernPaiute,
  /**
   * Southern Paiute
   */
  SouthernPaiute: v3_Race_SouthernPaiute,
  /**
   * Summit Lake
   */
  SummitLake: v3_Race_SummitLake,
  /**
   * Utu Utu Gwaitu Paiute
   */
  UtuUtuGwaituPaiute: v3_Race_UtuUtuGwaituPaiute,
  /**
   * Walker River
   */
  WalkerRiver: v3_Race_WalkerRiver,
  /**
   * Yerington Paiute
   */
  YeringtonPaiute: v3_Race_YeringtonPaiute,
  /**
   * Pamunkey
   */
  Pamunkey: v3_Race_Pamunkey,
  /**
   * Passamaquoddy
   */
  Passamaquoddy: v3_Race_Passamaquoddy,
  /**
   * Indian Township
   */
  IndianTownship: v3_Race_IndianTownship,
  /**
   * Pleasant Point Passamaquoddy
   */
  PleasantPointPassamaquoddy: v3_Race_PleasantPointPassamaquoddy,
  /**
   * Pawnee
   */
  Pawnee: v3_Race_Pawnee,
  /**
   * Oklahoma Pawnee
   */
  OklahomaPawnee: v3_Race_OklahomaPawnee,
  /**
   * Penobscot
   */
  Penobscot: v3_Race_Penobscot,
  /**
   * Peoria
   */
  Peoria: v3_Race_Peoria,
  /**
   * Oklahoma Peoria
   */
  OklahomaPeoria: v3_Race_OklahomaPeoria,
  /**
   * Pequot
   */
  Pequot: v3_Race_Pequot,
  /**
   * Marshantucket Pequot
   */
  MarshantucketPequot: v3_Race_MarshantucketPequot,
  /**
   * Pima
   */
  Pima: v3_Race_Pima,
  /**
   * Gila River Pima-Maricopa
   */
  GilaRiverPimaMaricopa: v3_Race_GilaRiverPimaMaricopa,
  /**
   * Salt River Pima-Maricopa
   */
  SaltRiverPimaMaricopa: v3_Race_SaltRiverPimaMaricopa,
  /**
   * Piscataway
   */
  Piscataway: v3_Race_Piscataway,
  /**
   * Pit River
   */
  PitRiver: v3_Race_PitRiver,
  /**
   * Pomo
   */
  Pomo: v3_Race_Pomo,
  /**
   * Central Pomo
   */
  CentralPomo: v3_Race_CentralPomo,
  /**
   * Dry Creek
   */
  DryCreek: v3_Race_DryCreek,
  /**
   * Eastern Pomo
   */
  EasternPomo: v3_Race_EasternPomo,
  /**
   * Kashia
   */
  Kashia: v3_Race_Kashia,
  /**
   * Northern Pomo
   */
  NorthernPomo: v3_Race_NorthernPomo,
  /**
   * Scotts Valley
   */
  ScottsValley: v3_Race_ScottsValley,
  /**
   * Stonyford
   */
  Stonyford: v3_Race_Stonyford,
  /**
   * Sulphur Bank
   */
  SulphurBank: v3_Race_SulphurBank,
  /**
   * Ponca
   */
  Ponca: v3_Race_Ponca,
  /**
   * Nebraska Ponca
   */
  NebraskaPonca: v3_Race_NebraskaPonca,
  /**
   * Oklahoma Ponca
   */
  OklahomaPonca: v3_Race_OklahomaPonca,
  /**
   * Potawatomi
   */
  Potawatomi: v3_Race_Potawatomi,
  /**
   * Citizen Band Potawatomi
   */
  CitizenBandPotawatomi: v3_Race_CitizenBandPotawatomi,
  /**
   * Forest County
   */
  ForestCounty: v3_Race_ForestCounty,
  /**
   * Hannahville
   */
  Hannahville: v3_Race_Hannahville,
  /**
   * Huron Potawatomi
   */
  HuronPotawatomi: v3_Race_HuronPotawatomi,
  /**
   * Pokagon Potawatomi
   */
  PokagonPotawatomi: v3_Race_PokagonPotawatomi,
  /**
   * Prairie Band
   */
  PrairieBand: v3_Race_PrairieBand,
  /**
   * Wisconsin Potawatomi
   */
  WisconsinPotawatomi: v3_Race_WisconsinPotawatomi,
  /**
   * Powhatan
   */
  Powhatan: v3_Race_Powhatan,
  /**
   * Pueblo
   */
  Pueblo: v3_Race_Pueblo,
  /**
   * Acoma
   */
  Acoma: v3_Race_Acoma,
  /**
   * Arizona Tewa
   */
  ArizonaTewa: v3_Race_ArizonaTewa,
  /**
   * Cochiti
   */
  Cochiti: v3_Race_Cochiti,
  /**
   * Hopi
   */
  Hopi: v3_Race_Hopi,
  /**
   * Isleta
   */
  Isleta: v3_Race_Isleta,
  /**
   * Jemez
   */
  Jemez: v3_Race_Jemez,
  /**
   * Keres
   */
  Keres: v3_Race_Keres,
  /**
   * Laguna
   */
  Laguna: v3_Race_Laguna,
  /**
   * Nambe
   */
  Nambe: v3_Race_Nambe,
  /**
   * Picuris
   */
  Picuris: v3_Race_Picuris,
  /**
   * Piro
   */
  Piro: v3_Race_Piro,
  /**
   * Pojoaque
   */
  Pojoaque: v3_Race_Pojoaque,
  /**
   * San Felipe
   */
  SanFelipe: v3_Race_SanFelipe,
  /**
   * San Ildefonso
   */
  SanIldefonso: v3_Race_SanIldefonso,
  /**
   * San Juan Pueblo
   */
  SanJuanPueblo: v3_Race_SanJuanPueblo,
  /**
   * San Juan De
   */
  SanJuanDe: v3_Race_SanJuanDe,
  /**
   * San Juan
   */
  SanJuan: v3_Race_SanJuan,
  /**
   * Sandia
   */
  Sandia: v3_Race_Sandia,
  /**
   * Santa Ana
   */
  SantaAna: v3_Race_SantaAna,
  /**
   * Santa Clara
   */
  SantaClara: v3_Race_SantaClara,
  /**
   * Santo Domingo
   */
  SantoDomingo: v3_Race_SantoDomingo,
  /**
   * Taos
   */
  Taos: v3_Race_Taos,
  /**
   * Tesuque
   */
  Tesuque: v3_Race_Tesuque,
  /**
   * Tewa
   */
  Tewa: v3_Race_Tewa,
  /**
   * Tigua
   */
  Tigua: v3_Race_Tigua,
  /**
   * Zia
   */
  Zia: v3_Race_Zia,
  /**
   * Zuni
   */
  Zuni: v3_Race_Zuni,
  /**
   * Puget Sound Salish
   */
  PugetSoundSalish: v3_Race_PugetSoundSalish,
  /**
   * Duwamish
   */
  Duwamish: v3_Race_Duwamish,
  /**
   * Kikiallus
   */
  Kikiallus: v3_Race_Kikiallus,
  /**
   * Lower Skagit
   */
  LowerSkagit: v3_Race_LowerSkagit,
  /**
   * Muckleshoot
   */
  Muckleshoot: v3_Race_Muckleshoot,
  /**
   * Nisqually
   */
  Nisqually: v3_Race_Nisqually,
  /**
   * Nooksack
   */
  Nooksack: v3_Race_Nooksack,
  /**
   * Port Madison
   */
  PortMadison: v3_Race_PortMadison,
  /**
   * Puyallup
   */
  Puyallup: v3_Race_Puyallup,
  /**
   * Samish
   */
  Samish: v3_Race_Samish,
  /**
   * Sauk-Suiattle
   */
  SaukSuiattle: v3_Race_SaukSuiattle,
  /**
   * Skokomish
   */
  Skokomish: v3_Race_Skokomish,
  /**
   * Skykomish
   */
  Skykomish: v3_Race_Skykomish,
  /**
   * Snohomish
   */
  Snohomish: v3_Race_Snohomish,
  /**
   * Snoqualmie
   */
  Snoqualmie: v3_Race_Snoqualmie,
  /**
   * Squaxin Island
   */
  SquaxinIsland: v3_Race_SquaxinIsland,
  /**
   * Steilacoom
   */
  Steilacoom: v3_Race_Steilacoom,
  /**
   * Stillaguamish
   */
  Stillaguamish: v3_Race_Stillaguamish,
  /**
   * Suquamish
   */
  Suquamish: v3_Race_Suquamish,
  /**
   * Swinomish
   */
  Swinomish: v3_Race_Swinomish,
  /**
   * Tulalip
   */
  Tulalip: v3_Race_Tulalip,
  /**
   * Upper Skagit
   */
  UpperSkagit: v3_Race_UpperSkagit,
  /**
   * Quapaw
   */
  Quapaw: v3_Race_Quapaw,
  /**
   * Quinault
   */
  Quinault: v3_Race_Quinault,
  /**
   * Rappahannock
   */
  Rappahannock: v3_Race_Rappahannock,
  /**
   * Reno-Sparks
   */
  RenoSparks: v3_Race_RenoSparks,
  /**
   * Round Valley
   */
  RoundValley: v3_Race_RoundValley,
  /**
   * Sac and Fox
   */
  SacAndFox: v3_Race_SacAndFox,
  /**
   * Iowa Sac and Fox
   */
  IowaSacAndFox: v3_Race_IowaSacAndFox,
  /**
   * Missouri Sac and Fox
   */
  MissouriSacAndFox: v3_Race_MissouriSacAndFox,
  /**
   * Oklahoma Sac and Fox
   */
  OklahomaSacAndFox: v3_Race_OklahomaSacAndFox,
  /**
   * Salinan
   */
  Salinan: v3_Race_Salinan,
  /**
   * Salish
   */
  Salish: v3_Race_Salish,
  /**
   * Salish and Kootenai
   */
  SalishAndKootenai: v3_Race_SalishAndKootenai,
  /**
   * Schaghticoke
   */
  Schaghticoke: v3_Race_Schaghticoke,
  /**
   * Scott Valley
   */
  ScottValley: v3_Race_ScottValley,
  /**
   * Seminole
   */
  Seminole: v3_Race_Seminole,
  /**
   * Big Cypress
   */
  BigCypress: v3_Race_BigCypress,
  /**
   * Brighton
   */
  Brighton: v3_Race_Brighton,
  /**
   * Florida Seminole
   */
  FloridaSeminole: v3_Race_FloridaSeminole,
  /**
   * Hollywood Seminole
   */
  HollywoodSeminole: v3_Race_HollywoodSeminole,
  /**
   * Oklahoma Seminole
   */
  OklahomaSeminole: v3_Race_OklahomaSeminole,
  /**
   * Serrano
   */
  Serrano: v3_Race_Serrano,
  /**
   * San Manual
   */
  SanManual: v3_Race_SanManual,
  /**
   * Shasta
   */
  Shasta: v3_Race_Shasta,
  /**
   * Shawnee
   */
  Shawnee: v3_Race_Shawnee,
  /**
   * Absentee Shawnee
   */
  AbsenteeShawnee: v3_Race_AbsenteeShawnee,
  /**
   * Eastern Shawnee
   */
  EasternShawnee: v3_Race_EasternShawnee,
  /**
   * Shinnecock
   */
  Shinnecock: v3_Race_Shinnecock,
  /**
   * Shoalwater Bay
   */
  ShoalwaterBay: v3_Race_ShoalwaterBay,
  /**
   * Shoshone
   */
  Shoshone: v3_Race_Shoshone,
  /**
   * Battle Mountain
   */
  BattleMountain: v3_Race_BattleMountain,
  /**
   * Duckwater
   */
  Duckwater: v3_Race_Duckwater,
  /**
   * Elko
   */
  Elko: v3_Race_Elko,
  /**
   * Ely
   */
  Ely: v3_Race_Ely,
  /**
   * Goshute
   */
  Goshute: v3_Race_Goshute,
  /**
   * Panamint
   */
  Panamint: v3_Race_Panamint,
  /**
   * Ruby Valley
   */
  RubyValley: v3_Race_RubyValley,
  /**
   * Skull Valley
   */
  SkullValley: v3_Race_SkullValley,
  /**
   * South Fork Shoshone
   */
  SouthForkShoshone: v3_Race_SouthForkShoshone,
  /**
   * Te-Moak Western Shoshone
   */
  TeMoakWesternShoshone: v3_Race_TeMoakWesternShoshone,
  /**
   * Timbi-Sha Shoshone
   */
  TimbiShaShoshone: v3_Race_TimbiShaShoshone,
  /**
   * Washakie
   */
  Washakie: v3_Race_Washakie,
  /**
   * Wind River Shoshone
   */
  WindRiverShoshone: v3_Race_WindRiverShoshone,
  /**
   * Yomba
   */
  Yomba: v3_Race_Yomba,
  /**
   * Shoshone Paiute
   */
  ShoshonePaiute: v3_Race_ShoshonePaiute,
  /**
   * Duck Valley
   */
  DuckValley: v3_Race_DuckValley,
  /**
   * Fallon
   */
  Fallon: v3_Race_Fallon,
  /**
   * Fort McDermitt
   */
  FortMcDermitt: v3_Race_FortMcDermitt,
  /**
   * Siletz
   */
  Siletz: v3_Race_Siletz,
  /**
   * Sioux
   */
  Sioux: v3_Race_Sioux,
  /**
   * Blackfoot Sioux
   */
  BlackfootSioux: v3_Race_BlackfootSioux,
  /**
   * Brule Sioux
   */
  BruleSioux: v3_Race_BruleSioux,
  /**
   * Cheyenne River Sioux
   */
  CheyenneRiverSioux: v3_Race_CheyenneRiverSioux,
  /**
   * Crow Creek Sioux
   */
  CrowCreekSioux: v3_Race_CrowCreekSioux,
  /**
   * Dakota Sioux
   */
  DakotaSioux: v3_Race_DakotaSioux,
  /**
   * Flandreau Santee
   */
  FlandreauSantee: v3_Race_FlandreauSantee,
  /**
   * Fort Peck
   */
  FortPeck: v3_Race_FortPeck,
  /**
   * Lake Traverse Sioux
   */
  LakeTraverseSioux: v3_Race_LakeTraverseSioux,
  /**
   * Lower Brule Sioux
   */
  LowerBruleSioux: v3_Race_LowerBruleSioux,
  /**
   * Lower Sioux
   */
  LowerSioux: v3_Race_LowerSioux,
  /**
   * Mdewakanton Sioux
   */
  MdewakantonSioux: v3_Race_MdewakantonSioux,
  /**
   * Miniconjou
   */
  Miniconjou: v3_Race_Miniconjou,
  /**
   * Oglala Sioux
   */
  OglalaSioux: v3_Race_OglalaSioux,
  /**
   * Pine Ridge Sioux
   */
  PineRidgeSioux: v3_Race_PineRidgeSioux,
  /**
   * Pipestone Sioux
   */
  PipestoneSioux: v3_Race_PipestoneSioux,
  /**
   * Prairie Island Sioux
   */
  PrairieIslandSioux: v3_Race_PrairieIslandSioux,
  /**
   * Prior Lake Sioux
   */
  PriorLakeSioux: v3_Race_PriorLakeSioux,
  /**
   * Rosebud Sioux
   */
  RosebudSioux: v3_Race_RosebudSioux,
  /**
   * Sans Arc Sioux
   */
  SansArcSioux: v3_Race_SansArcSioux,
  /**
   * Santee Sioux
   */
  SanteeSioux: v3_Race_SanteeSioux,
  /**
   * Sisseton-Wahpeton
   */
  SissetonWahpeton: v3_Race_SissetonWahpeton,
  /**
   * Sisseton Sioux
   */
  SissetonSioux: v3_Race_SissetonSioux,
  /**
   * Spirit Lake Sioux
   */
  SpiritLakeSioux: v3_Race_SpiritLakeSioux,
  /**
   * Standing Rock Sioux
   */
  StandingRockSioux: v3_Race_StandingRockSioux,
  /**
   * Teton Sioux
   */
  TetonSioux: v3_Race_TetonSioux,
  /**
   * Two Kettle Sioux
   */
  TwoKettleSioux: v3_Race_TwoKettleSioux,
  /**
   * Upper Sioux
   */
  UpperSioux: v3_Race_UpperSioux,
  /**
   * Wahpekute Sioux
   */
  WahpekuteSioux: v3_Race_WahpekuteSioux,
  /**
   * Wahpeton Sioux
   */
  WahpetonSioux: v3_Race_WahpetonSioux,
  /**
   * Wazhaza Sioux
   */
  WazhazaSioux: v3_Race_WazhazaSioux,
  /**
   * Yankton Sioux
   */
  YanktonSioux: v3_Race_YanktonSioux,
  /**
   * Yanktonai Sioux
   */
  YanktonaiSioux: v3_Race_YanktonaiSioux,
  /**
   * Siuslaw
   */
  Siuslaw: v3_Race_Siuslaw,
  /**
   * Spokane
   */
  Spokane: v3_Race_Spokane,
  /**
   * Stewart
   */
  Stewart: v3_Race_Stewart,
  /**
   * Stockbridge
   */
  Stockbridge: v3_Race_Stockbridge,
  /**
   * Susanville
   */
  Susanville: v3_Race_Susanville,
  /**
   * Tohono O'Odham
   */
  TohonoOQuoteOdham: v3_Race_TohonoOQuoteOdham,
  /**
   * Ak-Chin
   */
  AkChin: v3_Race_AkChin,
  /**
   * Gila Bend
   */
  GilaBend: v3_Race_GilaBend,
  /**
   * San Xavier
   */
  SanXavier: v3_Race_SanXavier,
  /**
   * Sells
   */
  Sells: v3_Race_Sells,
  /**
   * Tolowa
   */
  Tolowa: v3_Race_Tolowa,
  /**
   * Tonkawa
   */
  Tonkawa: v3_Race_Tonkawa,
  /**
   * Tygh
   */
  Tygh: v3_Race_Tygh,
  /**
   * Umatilla
   */
  Umatilla: v3_Race_Umatilla,
  /**
   * Umpqua
   */
  Umpqua: v3_Race_Umpqua,
  /**
   * Cow Creek Umpqua
   */
  CowCreekUmpqua: v3_Race_CowCreekUmpqua,
  /**
   * Ute
   */
  Ute: v3_Race_Ute,
  /**
   * Allen Canyon
   */
  AllenCanyon: v3_Race_AllenCanyon,
  /**
   * Uintah Ute
   */
  UintahUte: v3_Race_UintahUte,
  /**
   * Ute Mountain Ute
   */
  UteMountainUte: v3_Race_UteMountainUte,
  /**
   * Wailaki
   */
  Wailaki: v3_Race_Wailaki,
  /**
   * Walla-Walla
   */
  WallaWalla: v3_Race_WallaWalla,
  /**
   * Wampanoag
   */
  Wampanoag: v3_Race_Wampanoag,
  /**
   * Gay Head Wampanoag
   */
  GayHeadWampanoag: v3_Race_GayHeadWampanoag,
  /**
   * Mashpee Wampanoag
   */
  MashpeeWampanoag: v3_Race_MashpeeWampanoag,
  /**
   * Warm Springs
   */
  WarmSprings: v3_Race_WarmSprings,
  /**
   * Wascopum
   */
  Wascopum: v3_Race_Wascopum,
  /**
   * Washoe
   */
  Washoe: v3_Race_Washoe,
  /**
   * Alpine
   */
  Alpine: v3_Race_Alpine,
  /**
   * Carson
   */
  Carson: v3_Race_Carson,
  /**
   * Dresslerville
   */
  Dresslerville: v3_Race_Dresslerville,
  /**
   * Wichita
   */
  Wichita: v3_Race_Wichita,
  /**
   * Wind River
   */
  WindRiver: v3_Race_WindRiver,
  /**
   * Winnebago
   */
  Winnebago: v3_Race_Winnebago,
  /**
   * Ho-chunk
   */
  HoChunk: v3_Race_HoChunk,
  /**
   * Nebraska Winnebago
   */
  NebraskaWinnebago: v3_Race_NebraskaWinnebago,
  /**
   * Winnemucca
   */
  Winnemucca: v3_Race_Winnemucca,
  /**
   * Wintun
   */
  Wintun: v3_Race_Wintun,
  /**
   * Wiyot
   */
  Wiyot: v3_Race_Wiyot,
  /**
   * Table Bluff
   */
  TableBluff: v3_Race_TableBluff,
  /**
   * Yakama
   */
  Yakama: v3_Race_Yakama,
  /**
   * Yakama Cowlitz
   */
  YakamaCowlitz: v3_Race_YakamaCowlitz,
  /**
   * Yaqui
   */
  Yaqui: v3_Race_Yaqui,
  /**
   * Barrio Libre
   */
  BarrioLibre: v3_Race_BarrioLibre,
  /**
   * Pascua Yaqui
   */
  PascuaYaqui: v3_Race_PascuaYaqui,
  /**
   * Yavapai Apache
   */
  YavapaiApache: v3_Race_YavapaiApache,
  /**
   * Yokuts
   */
  Yokuts: v3_Race_Yokuts,
  /**
   * Chukchansi
   */
  Chukchansi: v3_Race_Chukchansi,
  /**
   * Tachi
   */
  Tachi: v3_Race_Tachi,
  /**
   * Tule River
   */
  TuleRiver: v3_Race_TuleRiver,
  /**
   * Yuchi
   */
  Yuchi: v3_Race_Yuchi,
  /**
   * Yuman
   */
  Yuman: v3_Race_Yuman,
  /**
   * Cocopah
   */
  Cocopah: v3_Race_Cocopah,
  /**
   * Havasupai
   */
  Havasupai: v3_Race_Havasupai,
  /**
   * Hualapai
   */
  Hualapai: v3_Race_Hualapai,
  /**
   * Maricopa
   */
  Maricopa: v3_Race_Maricopa,
  /**
   * Mohave
   */
  Mohave: v3_Race_Mohave,
  /**
   * Quechan
   */
  Quechan: v3_Race_Quechan,
  /**
   * Yavapai
   */
  Yavapai: v3_Race_Yavapai,
  /**
   * Yurok
   */
  Yurok: v3_Race_Yurok,
  /**
   * Coast Yurok
   */
  CoastYurok: v3_Race_CoastYurok,
  /**
   * Alaska Native
   */
  AlaskaNative: v3_Race_AlaskaNative,
  /**
   * Alaska Indian
   */
  AlaskaIndian: v3_Race_AlaskaIndian,
  /**
   * Alaskan Athabascan
   */
  AlaskanAthabascan: v3_Race_AlaskanAthabascan,
  /**
   * Ahtna
   */
  Ahtna: v3_Race_Ahtna,
  /**
   * Alatna
   */
  Alatna: v3_Race_Alatna,
  /**
   * Alexander
   */
  Alexander: v3_Race_Alexander,
  /**
   * Allakaket
   */
  Allakaket: v3_Race_Allakaket,
  /**
   * Alanvik
   */
  Alanvik: v3_Race_Alanvik,
  /**
   * Anvik
   */
  Anvik: v3_Race_Anvik,
  /**
   * Arctic
   */
  Arctic: v3_Race_Arctic,
  /**
   * Beaver
   */
  Beaver: v3_Race_Beaver,
  /**
   * Birch Creek
   */
  BirchCreek: v3_Race_BirchCreek,
  /**
   * Cantwell
   */
  Cantwell: v3_Race_Cantwell,
  /**
   * Chalkyitsik
   */
  Chalkyitsik: v3_Race_Chalkyitsik,
  /**
   * Chickaloon
   */
  Chickaloon: v3_Race_Chickaloon,
  /**
   * Chistochina
   */
  Chistochina: v3_Race_Chistochina,
  /**
   * Chitina
   */
  Chitina: v3_Race_Chitina,
  /**
   * Circle
   */
  Circle: v3_Race_Circle,
  /**
   * Cook Inlet
   */
  CookInlet: v3_Race_CookInlet,
  /**
   * Copper Center
   */
  CopperCenter: v3_Race_CopperCenter,
  /**
   * Copper River
   */
  CopperRiver: v3_Race_CopperRiver,
  /**
   * Dot Lake
   */
  DotLake: v3_Race_DotLake,
  /**
   * Doyon
   */
  Doyon: v3_Race_Doyon,
  /**
   * Eagle
   */
  Eagle: v3_Race_Eagle,
  /**
   * Eklutna
   */
  Eklutna: v3_Race_Eklutna,
  /**
   * Evansville
   */
  Evansville: v3_Race_Evansville,
  /**
   * Fort Yukon
   */
  FortYukon: v3_Race_FortYukon,
  /**
   * Gakona
   */
  Gakona: v3_Race_Gakona,
  /**
   * Galena
   */
  Galena: v3_Race_Galena,
  /**
   * Grayling
   */
  Grayling: v3_Race_Grayling,
  /**
   * Gulkana
   */
  Gulkana: v3_Race_Gulkana,
  /**
   * Healy Lake
   */
  HealyLake: v3_Race_HealyLake,
  /**
   * Holy Cross
   */
  HolyCross: v3_Race_HolyCross,
  /**
   * Hughes
   */
  Hughes: v3_Race_Hughes,
  /**
   * Huslia
   */
  Huslia: v3_Race_Huslia,
  /**
   * Iliamna
   */
  Iliamna: v3_Race_Iliamna,
  /**
   * Kaltag
   */
  Kaltag: v3_Race_Kaltag,
  /**
   * Kluti Kaah
   */
  KlutiKaah: v3_Race_KlutiKaah,
  /**
   * Knik
   */
  Knik: v3_Race_Knik,
  /**
   * Koyukuk
   */
  Koyukuk: v3_Race_Koyukuk,
  /**
   * Lake Minchumina
   */
  LakeMinchumina: v3_Race_LakeMinchumina,
  /**
   * Lime
   */
  Lime: v3_Race_Lime,
  /**
   * Mcgrath
   */
  Mcgrath: v3_Race_Mcgrath,
  /**
   * Manley Hot Springs
   */
  ManleyHotSprings: v3_Race_ManleyHotSprings,
  /**
   * Mentasta Lake
   */
  MentastaLake: v3_Race_MentastaLake,
  /**
   * Minto
   */
  Minto: v3_Race_Minto,
  /**
   * Nenana
   */
  Nenana: v3_Race_Nenana,
  /**
   * Nikolai
   */
  Nikolai: v3_Race_Nikolai,
  /**
   * Ninilchik
   */
  Ninilchik: v3_Race_Ninilchik,
  /**
   * Nondalton
   */
  Nondalton: v3_Race_Nondalton,
  /**
   * Northway
   */
  Northway: v3_Race_Northway,
  /**
   * Nulato
   */
  Nulato: v3_Race_Nulato,
  /**
   * Pedro Bay
   */
  PedroBay: v3_Race_PedroBay,
  /**
   * Rampart
   */
  Rampart: v3_Race_Rampart,
  /**
   * Ruby
   */
  Ruby: v3_Race_Ruby,
  /**
   * Salamatof
   */
  Salamatof: v3_Race_Salamatof,
  /**
   * Seldovia
   */
  Seldovia: v3_Race_Seldovia,
  /**
   * Slana
   */
  Slana: v3_Race_Slana,
  /**
   * Shageluk
   */
  Shageluk: v3_Race_Shageluk,
  /**
   * Stevens
   */
  Stevens: v3_Race_Stevens,
  /**
   * Stony River
   */
  StonyRiver: v3_Race_StonyRiver,
  /**
   * Takotna
   */
  Takotna: v3_Race_Takotna,
  /**
   * Tanacross
   */
  Tanacross: v3_Race_Tanacross,
  /**
   * Tanaina
   */
  Tanaina: v3_Race_Tanaina,
  /**
   * Tanana
   */
  Tanana: v3_Race_Tanana,
  /**
   * Tanana Chiefs
   */
  TananaChiefs: v3_Race_TananaChiefs,
  /**
   * Tazlina
   */
  Tazlina: v3_Race_Tazlina,
  /**
   * Telida
   */
  Telida: v3_Race_Telida,
  /**
   * Tetlin
   */
  Tetlin: v3_Race_Tetlin,
  /**
   * Tok
   */
  Tok: v3_Race_Tok,
  /**
   * Tyonek
   */
  Tyonek: v3_Race_Tyonek,
  /**
   * Venetie
   */
  Venetie: v3_Race_Venetie,
  /**
   * Wiseman
   */
  Wiseman: v3_Race_Wiseman,
  /**
   * Southeast Alaska
   */
  SoutheastAlaska: v3_Race_SoutheastAlaska,
  /**
   * Tlingit-Haida
   */
  TlingitHaida: v3_Race_TlingitHaida,
  /**
   * Angoon
   */
  Angoon: v3_Race_Angoon,
  /**
   * Central Council of Tlingit and Haida Tribes
   */
  CentralCouncilOfTlingitAndHaidaTribes: v3_Race_CentralCouncilOfTlingitAndHaidaTribes,
  /**
   * Chilkat
   */
  Chilkat: v3_Race_Chilkat,
  /**
   * Chilkoot
   */
  Chilkoot: v3_Race_Chilkoot,
  /**
   * Craig
   */
  Craig: v3_Race_Craig,
  /**
   * Douglas
   */
  Douglas: v3_Race_Douglas,
  /**
   * Haida
   */
  Haida: v3_Race_Haida,
  /**
   * Hoonah
   */
  Hoonah: v3_Race_Hoonah,
  /**
   * Hydaburg
   */
  Hydaburg: v3_Race_Hydaburg,
  /**
   * Kake
   */
  Kake: v3_Race_Kake,
  /**
   * Kasaan
   */
  Kasaan: v3_Race_Kasaan,
  /**
   * Kenaitze
   */
  Kenaitze: v3_Race_Kenaitze,
  /**
   * Ketchikan
   */
  Ketchikan: v3_Race_Ketchikan,
  /**
   * Klawock
   */
  Klawock: v3_Race_Klawock,
  /**
   * Pelican
   */
  Pelican: v3_Race_Pelican,
  /**
   * Petersburg
   */
  Petersburg: v3_Race_Petersburg,
  /**
   * Saxman
   */
  Saxman: v3_Race_Saxman,
  /**
   * Sitka
   */
  Sitka: v3_Race_Sitka,
  /**
   * Tenakee Springs
   */
  TenakeeSprings: v3_Race_TenakeeSprings,
  /**
   * Tlingit
   */
  Tlingit: v3_Race_Tlingit,
  /**
   * Wrangell
   */
  Wrangell: v3_Race_Wrangell,
  /**
   * Yakutat
   */
  Yakutat: v3_Race_Yakutat,
  /**
   * Tsimshian
   */
  Tsimshian: v3_Race_Tsimshian,
  /**
   * Metlakatla
   */
  Metlakatla: v3_Race_Metlakatla,
  /**
   * Eskimo
   */
  Eskimo: v3_Race_Eskimo,
  /**
   * Greenland Eskimo
   */
  GreenlandEskimo: v3_Race_GreenlandEskimo,
  /**
   * Inupiat Eskimo
   */
  InupiatEskimo: v3_Race_InupiatEskimo,
  /**
   * Ambler
   */
  Ambler: v3_Race_Ambler,
  /**
   * Anaktuvuk
   */
  Anaktuvuk: v3_Race_Anaktuvuk,
  /**
   * Anaktuvuk Pass
   */
  AnaktuvukPass: v3_Race_AnaktuvukPass,
  /**
   * Arctic Slope Inupiat
   */
  ArcticSlopeInupiat: v3_Race_ArcticSlopeInupiat,
  /**
   * Arctic Slope Corporation
   */
  ArcticSlopeCorporation: v3_Race_ArcticSlopeCorporation,
  /**
   * Atqasuk
   */
  Atqasuk: v3_Race_Atqasuk,
  /**
   * Barrow
   */
  Barrow: v3_Race_Barrow,
  /**
   * Bering Straits Inupiat
   */
  BeringStraitsInupiat: v3_Race_BeringStraitsInupiat,
  /**
   * Brevig Mission
   */
  BrevigMission: v3_Race_BrevigMission,
  /**
   * Buckland
   */
  Buckland: v3_Race_Buckland,
  /**
   * Chinik
   */
  Chinik: v3_Race_Chinik,
  /**
   * Council
   */
  Council: v3_Race_Council,
  /**
   * Deering
   */
  Deering: v3_Race_Deering,
  /**
   * Elim
   */
  Elim: v3_Race_Elim,
  /**
   * Golovin
   */
  Golovin: v3_Race_Golovin,
  /**
   * Inalik Diomede
   */
  InalikDiomede: v3_Race_InalikDiomede,
  /**
   * Inupiaq
   */
  Inupiaq: v3_Race_Inupiaq,
  /**
   * Kaktovik
   */
  Kaktovik: v3_Race_Kaktovik,
  /**
   * Kawerak
   */
  Kawerak: v3_Race_Kawerak,
  /**
   * Kiana
   */
  Kiana: v3_Race_Kiana,
  /**
   * Kivalina
   */
  Kivalina: v3_Race_Kivalina,
  /**
   * Kobuk
   */
  Kobuk: v3_Race_Kobuk,
  /**
   * Kotzebue
   */
  Kotzebue: v3_Race_Kotzebue,
  /**
   * Koyuk
   */
  Koyuk: v3_Race_Koyuk,
  /**
   * Kwiguk
   */
  Kwiguk: v3_Race_Kwiguk,
  /**
   * Mauneluk Inupiat
   */
  MaunelukInupiat: v3_Race_MaunelukInupiat,
  /**
   * Nana Inupiat
   */
  NanaInupiat: v3_Race_NanaInupiat,
  /**
   * Noatak
   */
  Noatak: v3_Race_Noatak,
  /**
   * Nome
   */
  Nome: v3_Race_Nome,
  /**
   * Noorvik
   */
  Noorvik: v3_Race_Noorvik,
  /**
   * Nuiqsut
   */
  Nuiqsut: v3_Race_Nuiqsut,
  /**
   * Point Hope
   */
  PointHope: v3_Race_PointHope,
  /**
   * Point Lay
   */
  PointLay: v3_Race_PointLay,
  /**
   * Selawik
   */
  Selawik: v3_Race_Selawik,
  /**
   * Shaktoolik
   */
  Shaktoolik: v3_Race_Shaktoolik,
  /**
   * Shishmaref
   */
  Shishmaref: v3_Race_Shishmaref,
  /**
   * Shungnak
   */
  Shungnak: v3_Race_Shungnak,
  /**
   * Solomon
   */
  Solomon: v3_Race_Solomon,
  /**
   * Teller
   */
  Teller: v3_Race_Teller,
  /**
   * Unalakleet
   */
  Unalakleet: v3_Race_Unalakleet,
  /**
   * Wainwright
   */
  Wainwright: v3_Race_Wainwright,
  /**
   * Wales
   */
  Wales: v3_Race_Wales,
  /**
   * White Mountain
   */
  WhiteMountain: v3_Race_WhiteMountain,
  /**
   * White Mountain Inupiat
   */
  WhiteMountainInupiat: v3_Race_WhiteMountainInupiat,
  /**
   * Mary's Igloo
   */
  MaryQuoteSIgloo: v3_Race_MaryQuoteSIgloo,
  /**
   * Siberian Eskimo
   */
  SiberianEskimo: v3_Race_SiberianEskimo,
  /**
   * Gambell
   */
  Gambell: v3_Race_Gambell,
  /**
   * Savoonga
   */
  Savoonga: v3_Race_Savoonga,
  /**
   * Siberian Yupik
   */
  SiberianYupik: v3_Race_SiberianYupik,
  /**
   * Yupik Eskimo
   */
  YupikEskimo: v3_Race_YupikEskimo,
  /**
   * Akiachak
   */
  Akiachak: v3_Race_Akiachak,
  /**
   * Akiak
   */
  Akiak: v3_Race_Akiak,
  /**
   * Alakanuk
   */
  Alakanuk: v3_Race_Alakanuk,
  /**
   * Aleknagik
   */
  Aleknagik: v3_Race_Aleknagik,
  /**
   * Andreafsky
   */
  Andreafsky: v3_Race_Andreafsky,
  /**
   * Aniak
   */
  Aniak: v3_Race_Aniak,
  /**
   * Atmautluak
   */
  Atmautluak: v3_Race_Atmautluak,
  /**
   * Bethel
   */
  Bethel: v3_Race_Bethel,
  /**
   * Bill Moore's Slough
   */
  BillMooreQuoteSSlough: v3_Race_BillMooreQuoteSSlough,
  /**
   * Bristol Bay Yupik
   */
  BristolBayYupik: v3_Race_BristolBayYupik,
  /**
   * Calista Yupik
   */
  CalistaYupik: v3_Race_CalistaYupik,
  /**
   * Chefornak
   */
  Chefornak: v3_Race_Chefornak,
  /**
   * Chevak
   */
  Chevak: v3_Race_Chevak,
  /**
   * Chuathbaluk
   */
  Chuathbaluk: v3_Race_Chuathbaluk,
  /**
   * Clark's Point
   */
  ClarkQuoteSPoint: v3_Race_ClarkQuoteSPoint,
  /**
   * Crooked Creek
   */
  CrookedCreek: v3_Race_CrookedCreek,
  /**
   * Dillingham
   */
  Dillingham: v3_Race_Dillingham,
  /**
   * Eek
   */
  Eek: v3_Race_Eek,
  /**
   * Ekuk
   */
  Ekuk: v3_Race_Ekuk,
  /**
   * Ekwok
   */
  Ekwok: v3_Race_Ekwok,
  /**
   * Emmonak
   */
  Emmonak: v3_Race_Emmonak,
  /**
   * Goodnews Bay
   */
  GoodnewsBay: v3_Race_GoodnewsBay,
  /**
   * Hooper Bay
   */
  HooperBay: v3_Race_HooperBay,
  /**
   * Iqurmuit (Russian Mission)
   */
  IqurmuitRussianMission: v3_Race_IqurmuitRussianMission,
  /**
   * Kalskag
   */
  Kalskag: v3_Race_Kalskag,
  /**
   * Kasigluk
   */
  Kasigluk: v3_Race_Kasigluk,
  /**
   * Kipnuk
   */
  Kipnuk: v3_Race_Kipnuk,
  /**
   * Koliganek
   */
  Koliganek: v3_Race_Koliganek,
  /**
   * Kongiganak
   */
  Kongiganak: v3_Race_Kongiganak,
  /**
   * Kotlik
   */
  Kotlik: v3_Race_Kotlik,
  /**
   * Kwethluk
   */
  Kwethluk: v3_Race_Kwethluk,
  /**
   * Kwigillingok
   */
  Kwigillingok: v3_Race_Kwigillingok,
  /**
   * Levelock
   */
  Levelock: v3_Race_Levelock,
  /**
   * Lower Kalskag
   */
  LowerKalskag: v3_Race_LowerKalskag,
  /**
   * Manokotak
   */
  Manokotak: v3_Race_Manokotak,
  /**
   * Marshall
   */
  Marshall: v3_Race_Marshall,
  /**
   * Mekoryuk
   */
  Mekoryuk: v3_Race_Mekoryuk,
  /**
   * Mountain Village
   */
  MountainVillage: v3_Race_MountainVillage,
  /**
   * Naknek
   */
  Naknek: v3_Race_Naknek,
  /**
   * Napaumute
   */
  Napaumute: v3_Race_Napaumute,
  /**
   * Napakiak
   */
  Napakiak: v3_Race_Napakiak,
  /**
   * Napaskiak
   */
  Napaskiak: v3_Race_Napaskiak,
  /**
   * Newhalen
   */
  Newhalen: v3_Race_Newhalen,
  /**
   * New Stuyahok
   */
  NewStuyahok: v3_Race_NewStuyahok,
  /**
   * Newtok
   */
  Newtok: v3_Race_Newtok,
  /**
   * Nightmute
   */
  Nightmute: v3_Race_Nightmute,
  /**
   * Nunapitchukv
   */
  Nunapitchukv: v3_Race_Nunapitchukv,
  /**
   * Oscarville
   */
  Oscarville: v3_Race_Oscarville,
  /**
   * Pilot Station
   */
  PilotStation: v3_Race_PilotStation,
  /**
   * Pitkas Point
   */
  PitkasPoint: v3_Race_PitkasPoint,
  /**
   * Platinum
   */
  Platinum: v3_Race_Platinum,
  /**
   * Portage Creek
   */
  PortageCreek: v3_Race_PortageCreek,
  /**
   * Quinhagak
   */
  Quinhagak: v3_Race_Quinhagak,
  /**
   * Red Devil
   */
  RedDevil: v3_Race_RedDevil,
  /**
   * St. Michael
   */
  StMichael: v3_Race_StMichael,
  /**
   * Scammon Bay
   */
  ScammonBay: v3_Race_ScammonBay,
  /**
   * Sheldon's Point
   */
  SheldonQuoteSPoint: v3_Race_SheldonQuoteSPoint,
  /**
   * Sleetmute
   */
  Sleetmute: v3_Race_Sleetmute,
  /**
   * Stebbins
   */
  Stebbins: v3_Race_Stebbins,
  /**
   * Togiak
   */
  Togiak: v3_Race_Togiak,
  /**
   * Toksook
   */
  Toksook: v3_Race_Toksook,
  /**
   * Tulukskak
   */
  Tulukskak: v3_Race_Tulukskak,
  /**
   * Tuntutuliak
   */
  Tuntutuliak: v3_Race_Tuntutuliak,
  /**
   * Tununak
   */
  Tununak: v3_Race_Tununak,
  /**
   * Twin Hills
   */
  TwinHills: v3_Race_TwinHills,
  /**
   * Georgetown
   */
  Georgetown_2: v3_Race_Georgetown_2,
  /**
   * St. Mary's
   */
  StMaryQuoteS: v3_Race_StMaryQuoteS,
  /**
   * Umkumiate
   */
  Umkumiate: v3_Race_Umkumiate,
  /**
   * Aleut
   */
  Aleut: v3_Race_Aleut,
  /**
   * Alutiiq Aleut
   */
  AlutiiqAleut: v3_Race_AlutiiqAleut,
  /**
   * Tatitlek
   */
  Tatitlek: v3_Race_Tatitlek,
  /**
   * Ugashik
   */
  Ugashik: v3_Race_Ugashik,
  /**
   * Bristol Bay Aleut
   */
  BristolBayAleut: v3_Race_BristolBayAleut,
  /**
   * Chignik
   */
  Chignik: v3_Race_Chignik,
  /**
   * Chignik Lake
   */
  ChignikLake: v3_Race_ChignikLake,
  /**
   * Egegik
   */
  Egegik: v3_Race_Egegik,
  /**
   * Igiugig
   */
  Igiugig: v3_Race_Igiugig,
  /**
   * Ivanof Bay
   */
  IvanofBay: v3_Race_IvanofBay,
  /**
   * King Salmon
   */
  KingSalmon: v3_Race_KingSalmon,
  /**
   * Kokhanok
   */
  Kokhanok: v3_Race_Kokhanok,
  /**
   * Perryville
   */
  Perryville: v3_Race_Perryville,
  /**
   * Pilot Point
   */
  PilotPoint: v3_Race_PilotPoint,
  /**
   * Port Heiden
   */
  PortHeiden: v3_Race_PortHeiden,
  /**
   * Chugach Aleut
   */
  ChugachAleut: v3_Race_ChugachAleut,
  /**
   * Chenega
   */
  Chenega: v3_Race_Chenega,
  /**
   * Chugach Corporation
   */
  ChugachCorporation: v3_Race_ChugachCorporation,
  /**
   * English Bay
   */
  EnglishBay: v3_Race_EnglishBay,
  /**
   * Port Graham
   */
  PortGraham: v3_Race_PortGraham,
  /**
   * Eyak
   */
  Eyak: v3_Race_Eyak,
  /**
   * Koniag Aleut
   */
  KoniagAleut: v3_Race_KoniagAleut,
  /**
   * Akhiok
   */
  Akhiok: v3_Race_Akhiok,
  /**
   * Agdaagux
   */
  Agdaagux: v3_Race_Agdaagux,
  /**
   * Karluk
   */
  Karluk: v3_Race_Karluk,
  /**
   * Kodiak
   */
  Kodiak: v3_Race_Kodiak,
  /**
   * Larsen Bay
   */
  LarsenBay: v3_Race_LarsenBay,
  /**
   * Old Harbor
   */
  OldHarbor: v3_Race_OldHarbor,
  /**
   * Ouzinkie
   */
  Ouzinkie: v3_Race_Ouzinkie,
  /**
   * Port Lions
   */
  PortLions: v3_Race_PortLions,
  /**
   * Sugpiaq
   */
  Sugpiaq: v3_Race_Sugpiaq,
  /**
   * Suqpigaq
   */
  Suqpigaq: v3_Race_Suqpigaq,
  /**
   * Unangan Aleut
   */
  UnanganAleut: v3_Race_UnanganAleut,
  /**
   * Akutan
   */
  Akutan: v3_Race_Akutan,
  /**
   * Aleut Corporation
   */
  AleutCorporation: v3_Race_AleutCorporation,
  /**
   * Aleutian
   */
  Aleutian: v3_Race_Aleutian,
  /**
   * Aleutian Islander
   */
  AleutianIslander: v3_Race_AleutianIslander,
  /**
   * Atka
   */
  Atka: v3_Race_Atka,
  /**
   * Belkofski
   */
  Belkofski: v3_Race_Belkofski,
  /**
   * Chignik Lagoon
   */
  ChignikLagoon: v3_Race_ChignikLagoon,
  /**
   * King Cove
   */
  KingCove: v3_Race_KingCove,
  /**
   * False Pass
   */
  FalsePass: v3_Race_FalsePass,
  /**
   * Nelson Lagoon
   */
  NelsonLagoon: v3_Race_NelsonLagoon,
  /**
   * Nikolski
   */
  Nikolski: v3_Race_Nikolski,
  /**
   * Pauloff Harbor
   */
  PauloffHarbor: v3_Race_PauloffHarbor,
  /**
   * Qagan Toyagungin
   */
  QaganToyagungin: v3_Race_QaganToyagungin,
  /**
   * Qawalangin
   */
  Qawalangin: v3_Race_Qawalangin,
  /**
   * St. George
   */
  StGeorge: v3_Race_StGeorge,
  /**
   * St. Paul
   */
  StPaul: v3_Race_StPaul,
  /**
   * Sand Point
   */
  SandPoint: v3_Race_SandPoint,
  /**
   * South Naknek
   */
  SouthNaknek: v3_Race_SouthNaknek,
  /**
   * Unalaska
   */
  Unalaska: v3_Race_Unalaska,
  /**
   * Unga
   */
  Unga: v3_Race_Unga,
  /**
   * Asian
   */
  Asian: v3_Race_Asian,
  /**
   * Asian Indian
   */
  AsianIndian: v3_Race_AsianIndian,
  /**
   * Bangladeshi
   */
  Bangladeshi: v3_Race_Bangladeshi,
  /**
   * Bhutanese
   */
  Bhutanese: v3_Race_Bhutanese,
  /**
   * Burmese
   */
  Burmese: v3_Race_Burmese,
  /**
   * Cambodian
   */
  Cambodian: v3_Race_Cambodian,
  /**
   * Chinese
   */
  Chinese: v3_Race_Chinese,
  /**
   * Taiwanese
   */
  Taiwanese: v3_Race_Taiwanese,
  /**
   * Filipino
   */
  Filipino: v3_Race_Filipino,
  /**
   * Hmong
   */
  Hmong: v3_Race_Hmong,
  /**
   * Indonesian
   */
  Indonesian: v3_Race_Indonesian,
  /**
   * Japanese
   */
  Japanese: v3_Race_Japanese,
  /**
   * Korean
   */
  Korean: v3_Race_Korean,
  /**
   * Laotian
   */
  Laotian: v3_Race_Laotian,
  /**
   * Malaysian
   */
  Malaysian: v3_Race_Malaysian,
  /**
   * Okinawan
   */
  Okinawan: v3_Race_Okinawan,
  /**
   * Pakistani
   */
  Pakistani: v3_Race_Pakistani,
  /**
   * Sri Lankan
   */
  SriLankan: v3_Race_SriLankan,
  /**
   * Thai
   */
  Thai: v3_Race_Thai,
  /**
   * Vietnamese
   */
  Vietnamese: v3_Race_Vietnamese,
  /**
   * Iwo Jiman
   */
  IwoJiman: v3_Race_IwoJiman,
  /**
   * Maldivian
   */
  Maldivian: v3_Race_Maldivian,
  /**
   * Nepalese
   */
  Nepalese: v3_Race_Nepalese,
  /**
   * Singaporean
   */
  Singaporean: v3_Race_Singaporean,
  /**
   * Madagascar
   */
  Madagascar: v3_Race_Madagascar,
  /**
   * Black or African American
   */
  BlackOrAfricanAmerican: v3_Race_BlackOrAfricanAmerican,
  /**
   * Black
   */
  Black: v3_Race_Black,
  /**
   * African American
   */
  AfricanAmerican: v3_Race_AfricanAmerican,
  /**
   * African
   */
  African: v3_Race_African,
  /**
   * Botswanan
   */
  Botswanan: v3_Race_Botswanan,
  /**
   * Ethiopian
   */
  Ethiopian: v3_Race_Ethiopian,
  /**
   * Liberian
   */
  Liberian: v3_Race_Liberian,
  /**
   * Namibian
   */
  Namibian: v3_Race_Namibian,
  /**
   * Nigerian
   */
  Nigerian: v3_Race_Nigerian,
  /**
   * Zairean
   */
  Zairean: v3_Race_Zairean,
  /**
   * Bahamian
   */
  Bahamian: v3_Race_Bahamian,
  /**
   * Barbadian
   */
  Barbadian: v3_Race_Barbadian,
  /**
   * Dominican
   */
  Dominican: v3_Race_Dominican,
  /**
   * Dominica Islander
   */
  DominicaIslander: v3_Race_DominicaIslander,
  /**
   * Haitian
   */
  Haitian: v3_Race_Haitian,
  /**
   * Jamaican
   */
  Jamaican: v3_Race_Jamaican,
  /**
   * Tobagoan
   */
  Tobagoan: v3_Race_Tobagoan,
  /**
   * Trinidadian
   */
  Trinidadian: v3_Race_Trinidadian,
  /**
   * West Indian
   */
  WestIndian: v3_Race_WestIndian,
  /**
   * Native Hawaiian or Other Pacific Islander
   */
  NativeHawaiianOrOtherPacificIslander: v3_Race_NativeHawaiianOrOtherPacificIslander,
  /**
   * Polynesian
   */
  Polynesian: v3_Race_Polynesian,
  /**
   * Native Hawaiian
   */
  NativeHawaiian: v3_Race_NativeHawaiian,
  /**
   * Samoan
   */
  Samoan: v3_Race_Samoan,
  /**
   * Tahitian
   */
  Tahitian: v3_Race_Tahitian,
  /**
   * Tongan
   */
  Tongan: v3_Race_Tongan,
  /**
   * Tokelauan
   */
  Tokelauan: v3_Race_Tokelauan,
  /**
   * Micronesian
   */
  Micronesian: v3_Race_Micronesian,
  /**
   * Guamanian or Chamorro
   */
  GuamanianOrChamorro: v3_Race_GuamanianOrChamorro,
  /**
   * Guamanian
   */
  Guamanian: v3_Race_Guamanian,
  /**
   * Chamorro
   */
  Chamorro: v3_Race_Chamorro,
  /**
   * Mariana Islander
   */
  MarianaIslander: v3_Race_MarianaIslander,
  /**
   * Marshallese
   */
  Marshallese: v3_Race_Marshallese,
  /**
   * Palauan
   */
  Palauan: v3_Race_Palauan,
  /**
   * Carolinian
   */
  Carolinian: v3_Race_Carolinian,
  /**
   * Kosraean
   */
  Kosraean: v3_Race_Kosraean,
  /**
   * Pohnpeian
   */
  Pohnpeian: v3_Race_Pohnpeian,
  /**
   * Saipanese
   */
  Saipanese: v3_Race_Saipanese,
  /**
   * Kiribati
   */
  Kiribati: v3_Race_Kiribati,
  /**
   * Chuukese
   */
  Chuukese: v3_Race_Chuukese,
  /**
   * Yapese
   */
  Yapese: v3_Race_Yapese,
  /**
   * Melanesian
   */
  Melanesian: v3_Race_Melanesian,
  /**
   * Fijian
   */
  Fijian: v3_Race_Fijian,
  /**
   * Papua New Guinean
   */
  PapuaNewGuinean: v3_Race_PapuaNewGuinean,
  /**
   * Solomon Islander
   */
  SolomonIslander: v3_Race_SolomonIslander,
  /**
   * New Hebrides
   */
  NewHebrides: v3_Race_NewHebrides,
  /**
   * White
   */
  White: v3_Race_White,
  /**
   * European
   */
  European: v3_Race_European,
  /**
   * Armenian
   */
  Armenian: v3_Race_Armenian,
  /**
   * English
   */
  English: v3_Race_English,
  /**
   * French
   */
  French: v3_Race_French,
  /**
   * German
   */
  German: v3_Race_German,
  /**
   * Irish
   */
  Irish: v3_Race_Irish,
  /**
   * Italian
   */
  Italian: v3_Race_Italian,
  /**
   * Polish
   */
  Polish: v3_Race_Polish,
  /**
   * Scottish
   */
  Scottish: v3_Race_Scottish,
  /**
   * Middle Eastern or North African
   */
  MiddleEasternOrNorthAfrican: v3_Race_MiddleEasternOrNorthAfrican,
  /**
   * Assyrian
   */
  Assyrian: v3_Race_Assyrian,
  /**
   * Egyptian
   */
  Egyptian: v3_Race_Egyptian,
  /**
   * Iranian
   */
  Iranian: v3_Race_Iranian,
  /**
   * Iraqi
   */
  Iraqi: v3_Race_Iraqi,
  /**
   * Lebanese
   */
  Lebanese: v3_Race_Lebanese,
  /**
   * Palestinian
   */
  Palestinian: v3_Race_Palestinian,
  /**
   * Syrian
   */
  Syrian: v3_Race_Syrian,
  /**
   * Afghanistani
   */
  Afghanistani: v3_Race_Afghanistani,
  /**
   * Israeili
   */
  Israeili: v3_Race_Israeili,
  /**
   * Arab
   */
  Arab: v3_Race_Arab,
  /**
   * Note that this term remains in the table for completeness, even though within HL7, the notion of Other code is deprecated.
   */
  OtherRace: v3_Race_OtherRace,
  /**
   * Note that this term remains in the table for completeness, even though within HL7, the notion of Other code is deprecated.
   */
  OtherPacificIslander: v3_Race_OtherPacificIslander,
};
/**
 *  Conveys prohibited actions which an information custodian, receiver, or user is not permitted to perform unless otherwise authorized or permitted under specified circumstances.  Examples:
 *   prohibit redisclosure without consent directive
 */
export const V3RefrainPolicy = {
  /**
   * Prohibition on disclosure without information subject's authorization.
   */
  NoDisclosureWithoutSubjectAuthorization: v3_ActCode_NoDisclosureWithoutSubjectAuthorization,
  /**
   * Prohibition on collection or storage of the information.
   */
  NoCollection: v3_ActCode_NoCollection,
  /**
   * Prohibition on disclosure without organizational approved patient restriction.
   */
  NoDisclosureWithoutConsentDirective: v3_ActCode_NoDisclosureWithoutConsentDirective,
  /**
   * Prohibition on disclosure without a consent directive from the information subject.
   */
  NoDisclosureWithoutInformationSubjectQuoteSConsentDirective: v3_ActCode_NoDisclosureWithoutInformationSubjectQuoteSConsentDirective,
  /**
   * Prohibition on Integration into other records.
   */
  NoIntegration: v3_ActCode_NoIntegration,
  /**
   * Prohibition on disclosure except to entities on specific access list.
   */
  NoUnlistedEntityDisclosure: v3_ActCode_NoUnlistedEntityDisclosure,
  /**
   * Prohibition on disclosure without an interagency service agreement or memorandum of understanding (MOU).
   */
  NoDisclosureWithoutMOU: v3_ActCode_NoDisclosureWithoutMOU,
  /**
   * Prohibition on disclosure without organizational authorization.
   */
  NoDisclosureWithoutOrganizationalAuthorization: v3_ActCode_NoDisclosureWithoutOrganizationalAuthorization,
  /**
   * Prohibition on disclosing information to patient, family or caregivers without attending provider's authorization.
   *
   *
   *                            Usage Note: The information may be labeled with the ActInformationSensitivity TBOO code, triggering application of this RefrainPolicy code as a handling caveat controlling access.
   *
   *                         Maps to FHIR NOPAT: Typically, this is used on an Alert resource, when the alert records information on patient abuse or non-compliance.
   *
   *                         FHIR print name is "keep information from patient". Maps to the French realm - code: INVISIBLE_PATIENT.
   *
   *
   *                            displayName: Document non visible par le patient
   *                            codingScheme: 1.2.250.1.213.1.1.4.13
   *
   *                         French use case:  A label for documents that the author  chose to hide from the patient until the content can be disclose to the patient in a face to face meeting between a healthcare professional and the patient (in French law some results like cancer diagnosis or AIDS diagnosis must be announced to the patient by a healthcare professional and should not be find out by the patient alone).
   */
  NoDisclosureToPatientFamilyOrCaregiversWithoutAttendingProviderQuoteSAuthorization: v3_ActCode_NoDisclosureToPatientFamilyOrCaregiversWithoutAttendingProviderQuoteSAuthorization,
  /**
   * Prohibition on collection of the information beyond time necessary to accomplish authorized purpose of use is prohibited.
   */
  NoCollectionBeyondPurposeOfUse: v3_ActCode_NoCollectionBeyondPurposeOfUse,
  /**
   * Prohibition on redisclosure without patient consent directive.
   */
  NoRedisclosureWithoutConsentDirective: v3_ActCode_NoRedisclosureWithoutConsentDirective,
  /**
   * Prohibition on redisclosure without a consent directive from the information subject.
   */
  NoRedisclosureWithoutInformationSubjectQuoteSConsentDirective: v3_ActCode_NoRedisclosureWithoutInformationSubjectQuoteSConsentDirective,
  /**
   * Prohibition on disclosure without authorization under jurisdictional law.
   */
  NoDisclosureWithoutJurisdictionalAuthorization: v3_ActCode_NoDisclosureWithoutJurisdictionalAuthorization,
  /**
   * Prohibition on associating de-identified or pseudonymized information with other information in a manner that could or does result in disclosing information intended to be masked.
   */
  NoRelinking: v3_ActCode_NoRelinking,
  /**
   * Prohibition on use of the information beyond the purpose of use initially authorized.
   */
  NoReuseBeyondPurposeOfUse: v3_ActCode_NoReuseBeyondPurposeOfUse,
  /**
   * Prohibition on disclosure except to principals with access permission to specific VIP information.
   */
  NoUnauthorizedVIPDisclosure: v3_ActCode_NoUnauthorizedVIPDisclosure,
  /**
   * Prohibition on disclosure except as permitted by the information originator.
   */
  NoDisclosureWithoutOriginatorAuthorization: v3_ActCode_NoDisclosureWithoutOriginatorAuthorization,
  /**
   * Conveys prohibited actions which an information custodian, receiver, or user is not permitted to perform unless otherwise authorized or permitted under specified circumstances.
   *
   *
   *
   *
   *                            Usage Notes: ISO 22600-2 species that a Refrain Policy "defines actions the subjects must refrain from performing".  Per HL7 Composite Security and Privacy Domain Analysis Model:  May be used to indicate that a specific action is prohibited based on specific access control attributes e.g., purpose of use, information type, user role, etc.
   */
  RefrainPolicy: v3_ActCode_RefrainPolicy,
};
const v3_RelationalOperator_Contains: Coding = {
  code: 'CT',
  display: 'Contains',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RelationalOperator',
};
const v3_RelationalOperator_Equal: Coding = {
  code: 'EQ',
  display: 'Equal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RelationalOperator',
};
const v3_RelationalOperator_GreaterThanOrEqual: Coding = {
  code: 'GE',
  display: 'Greater than or equal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RelationalOperator',
};
const v3_RelationalOperator_Generic: Coding = {
  code: 'GN',
  display: 'Generic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RelationalOperator',
};
const v3_RelationalOperator_GreaterThan: Coding = {
  code: 'GT',
  display: 'Greater than',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RelationalOperator',
};
const v3_RelationalOperator_LessThanOrEqual: Coding = {
  code: 'LE',
  display: 'Less than or equal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RelationalOperator',
};
const v3_RelationalOperator_LessThan: Coding = {
  code: 'LT',
  display: 'Less than',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RelationalOperator',
};
const v3_RelationalOperator_NotEqual: Coding = {
  code: 'NE',
  display: 'Not Equal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RelationalOperator',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3RelationalOperator = {
  /**
   * Specified set of things includes value being evaluated.
   */
  Contains: v3_RelationalOperator_Contains,
  /**
   * Equal condition applied to comparisons.
   */
  Equal: v3_RelationalOperator_Equal,
  /**
   * Greater than or equal condition applied to comparisons.
   */
  GreaterThanOrEqual: v3_RelationalOperator_GreaterThanOrEqual,
  /**
   * A generic comparison selects a record for inclusion in the response if the beginning of the designated element value matches the select string.
   */
  Generic: v3_RelationalOperator_Generic,
  /**
   * Greater than condition applied to comparisons.
   */
  GreaterThan: v3_RelationalOperator_GreaterThan,
  /**
   * Less than or equal condition applied to comparisons.
   */
  LessThanOrEqual: v3_RelationalOperator_LessThanOrEqual,
  /**
   * Less than condition applied to comparisons.
   */
  LessThan: v3_RelationalOperator_LessThan,
  /**
   * Not equal condition applied to comparisons.
   */
  NotEqual: v3_RelationalOperator_NotEqual,
};
const v3_RelationshipConjunction_And: Coding = {
  code: 'AND',
  display: 'and',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RelationshipConjunction',
};
const v3_RelationshipConjunction_Or: Coding = {
  code: 'OR',
  display: 'or',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RelationshipConjunction',
};
const v3_RelationshipConjunction_ExclusiveOr: Coding = {
  code: 'XOR',
  display: 'exclusive or',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RelationshipConjunction',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3RelationshipConjunction = {
  /**
   * This condition must be true.
   */
  And: v3_RelationshipConjunction_And,
  /**
   * At least one of the condition among all OR conditions must be true.
   */
  Or: v3_RelationshipConjunction_Or,
  /**
   * One and only one of the XOR conditions must be true.
   */
  ExclusiveOr: v3_RelationshipConjunction_ExclusiveOr,
};
const v3_ReligiousAffiliation_Adventist: Coding = {
  code: '1001',
  display: 'Adventist',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_AfricanReligions: Coding = {
  code: '1002',
  display: 'African Religions',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_AfroCaribbeanReligions: Coding = {
  code: '1003',
  display: 'Afro-Caribbean Religions',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Agnosticism: Coding = {
  code: '1004',
  display: 'Agnosticism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Anglican: Coding = {
  code: '1005',
  display: 'Anglican',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Animism: Coding = {
  code: '1006',
  display: 'Animism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Atheism: Coding = {
  code: '1007',
  display: 'Atheism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_BabiAndBahaQuoteIFaiths: Coding = {
  code: '1008',
  display: "Babi & Baha'I faiths",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Baptist: Coding = {
  code: '1009',
  display: 'Baptist',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Bon: Coding = {
  code: '1010',
  display: 'Bon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_CaoDai: Coding = {
  code: '1011',
  display: 'Cao Dai',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Celticism: Coding = {
  code: '1012',
  display: 'Celticism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_ChristianNonCatholicNonSpecific: Coding = {
  code: '1013',
  display: 'Christian (non-Catholic, non-specific)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Confucianism: Coding = {
  code: '1014',
  display: 'Confucianism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_CybercultureReligions: Coding = {
  code: '1015',
  display: 'Cyberculture Religions',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Divination: Coding = {
  code: '1016',
  display: 'Divination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_FourthWay: Coding = {
  code: '1017',
  display: 'Fourth Way',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_FreeDaism: Coding = {
  code: '1018',
  display: 'Free Daism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Gnosis: Coding = {
  code: '1019',
  display: 'Gnosis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Hinduism: Coding = {
  code: '1020',
  display: 'Hinduism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Humanism: Coding = {
  code: '1021',
  display: 'Humanism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Independent: Coding = {
  code: '1022',
  display: 'Independent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Islam: Coding = {
  code: '1023',
  display: 'Islam',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Jainism: Coding = {
  code: '1024',
  display: 'Jainism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_JehovahQuoteSWitnesses: Coding = {
  code: '1025',
  display: "Jehovah's Witnesses",
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Judaism: Coding = {
  code: '1026',
  display: 'Judaism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_LatterDaySaints: Coding = {
  code: '1027',
  display: 'Latter Day Saints',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Lutheran: Coding = {
  code: '1028',
  display: 'Lutheran',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Mahayana: Coding = {
  code: '1029',
  display: 'Mahayana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Meditation: Coding = {
  code: '1030',
  display: 'Meditation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_MessianicJudaism: Coding = {
  code: '1031',
  display: 'Messianic Judaism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Mitraism: Coding = {
  code: '1032',
  display: 'Mitraism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_NewAge: Coding = {
  code: '1033',
  display: 'New Age',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_NonRomanCatholic: Coding = {
  code: '1034',
  display: 'non-Roman Catholic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Occult: Coding = {
  code: '1035',
  display: 'Occult',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Orthodox: Coding = {
  code: '1036',
  display: 'Orthodox',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Paganism: Coding = {
  code: '1037',
  display: 'Paganism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Pentecostal: Coding = {
  code: '1038',
  display: 'Pentecostal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_ProcessThe: Coding = {
  code: '1039',
  display: 'Process, The',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_ReformedPresbyterian: Coding = {
  code: '1040',
  display: 'Reformed/Presbyterian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_RomanCatholicChurch: Coding = {
  code: '1041',
  display: 'Roman Catholic Church',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Satanism: Coding = {
  code: '1042',
  display: 'Satanism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Scientology: Coding = {
  code: '1043',
  display: 'Scientology',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Shamanism: Coding = {
  code: '1044',
  display: 'Shamanism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_ShiiteIslam: Coding = {
  code: '1045',
  display: 'Shiite (Islam)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Shinto: Coding = {
  code: '1046',
  display: 'Shinto',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Sikism: Coding = {
  code: '1047',
  display: 'Sikism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Spiritualism: Coding = {
  code: '1048',
  display: 'Spiritualism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_SunniIslam: Coding = {
  code: '1049',
  display: 'Sunni (Islam)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Taoism: Coding = {
  code: '1050',
  display: 'Taoism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Theravada: Coding = {
  code: '1051',
  display: 'Theravada',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_UnitarianUniversalism: Coding = {
  code: '1052',
  display: 'Unitarian-Universalism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_UniversalLifeChurch: Coding = {
  code: '1053',
  display: 'Universal Life Church',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_VajrayanaTibetan: Coding = {
  code: '1054',
  display: 'Vajrayana (Tibetan)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Veda: Coding = {
  code: '1055',
  display: 'Veda',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Voodoo: Coding = {
  code: '1056',
  display: 'Voodoo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Wicca: Coding = {
  code: '1057',
  display: 'Wicca',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Yaohushua: Coding = {
  code: '1058',
  display: 'Yaohushua',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_ZenBuddhism: Coding = {
  code: '1059',
  display: 'Zen Buddhism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Zoroastrianism: Coding = {
  code: '1060',
  display: 'Zoroastrianism',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_AssemblyOfGod: Coding = {
  code: '1061',
  display: 'Assembly of God',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Brethren: Coding = {
  code: '1062',
  display: 'Brethren',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_ChristianScientist: Coding = {
  code: '1063',
  display: 'Christian Scientist',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_ChurchOfChrist: Coding = {
  code: '1064',
  display: 'Church of Christ',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_ChurchOfGod: Coding = {
  code: '1065',
  display: 'Church of God',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Congregational: Coding = {
  code: '1066',
  display: 'Congregational',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_DisciplesOfChrist: Coding = {
  code: '1067',
  display: 'Disciples of Christ',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_EasternOrthodox: Coding = {
  code: '1068',
  display: 'Eastern Orthodox',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Episcopalian: Coding = {
  code: '1069',
  display: 'Episcopalian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_EvangelicalCovenant: Coding = {
  code: '1070',
  display: 'Evangelical Covenant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Friends: Coding = {
  code: '1071',
  display: 'Friends',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_FullGospel: Coding = {
  code: '1072',
  display: 'Full Gospel',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Methodist: Coding = {
  code: '1073',
  display: 'Methodist',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_NativeAmerican: Coding = {
  code: '1074',
  display: 'Native American',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Nazarene: Coding = {
  code: '1075',
  display: 'Nazarene',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Presbyterian: Coding = {
  code: '1076',
  display: 'Presbyterian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Protestant: Coding = {
  code: '1077',
  display: 'Protestant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_ProtestantNoDenomination: Coding = {
  code: '1078',
  display: 'Protestant, No Denomination',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_Reformed: Coding = {
  code: '1079',
  display: 'Reformed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_SalvationArmy: Coding = {
  code: '1080',
  display: 'Salvation Army',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_UnitarianUniversalist: Coding = {
  code: '1081',
  display: 'Unitarian Universalist',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
const v3_ReligiousAffiliation_UnitedChurchOfChrist: Coding = {
  code: '1082',
  display: 'United Church of Christ',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
};
/**
 *  Assigment of spiritual faith affiliation
 */
export const V3ReligiousAffiliation = {
  /**
   * Adventist
   */
  Adventist: v3_ReligiousAffiliation_Adventist,
  /**
   * African Religions
   */
  AfricanReligions: v3_ReligiousAffiliation_AfricanReligions,
  /**
   * Afro-Caribbean Religions
   */
  AfroCaribbeanReligions: v3_ReligiousAffiliation_AfroCaribbeanReligions,
  /**
   * Agnosticism
   */
  Agnosticism: v3_ReligiousAffiliation_Agnosticism,
  /**
   * Anglican
   */
  Anglican: v3_ReligiousAffiliation_Anglican,
  /**
   * Animism
   */
  Animism: v3_ReligiousAffiliation_Animism,
  /**
   * Atheism
   */
  Atheism: v3_ReligiousAffiliation_Atheism,
  /**
   * Babi & Baha'I faiths
   */
  BabiAndBahaQuoteIFaiths: v3_ReligiousAffiliation_BabiAndBahaQuoteIFaiths,
  /**
   * Baptist
   */
  Baptist: v3_ReligiousAffiliation_Baptist,
  /**
   * Bon
   */
  Bon: v3_ReligiousAffiliation_Bon,
  /**
   * Cao Dai
   */
  CaoDai: v3_ReligiousAffiliation_CaoDai,
  /**
   * Celticism
   */
  Celticism: v3_ReligiousAffiliation_Celticism,
  /**
   * Christian (non-Catholic, non-specific)
   */
  ChristianNonCatholicNonSpecific: v3_ReligiousAffiliation_ChristianNonCatholicNonSpecific,
  /**
   * Confucianism
   */
  Confucianism: v3_ReligiousAffiliation_Confucianism,
  /**
   * Cyberculture Religions
   */
  CybercultureReligions: v3_ReligiousAffiliation_CybercultureReligions,
  /**
   * Divination
   */
  Divination: v3_ReligiousAffiliation_Divination,
  /**
   * Fourth Way
   */
  FourthWay: v3_ReligiousAffiliation_FourthWay,
  /**
   * Free Daism
   */
  FreeDaism: v3_ReligiousAffiliation_FreeDaism,
  /**
   * Gnosis
   */
  Gnosis: v3_ReligiousAffiliation_Gnosis,
  /**
   * Hinduism
   */
  Hinduism: v3_ReligiousAffiliation_Hinduism,
  /**
   * Humanism
   */
  Humanism: v3_ReligiousAffiliation_Humanism,
  /**
   * Independent
   */
  Independent: v3_ReligiousAffiliation_Independent,
  /**
   * Islam
   */
  Islam: v3_ReligiousAffiliation_Islam,
  /**
   * Jainism
   */
  Jainism: v3_ReligiousAffiliation_Jainism,
  /**
   * Jehovah's Witnesses
   */
  JehovahQuoteSWitnesses: v3_ReligiousAffiliation_JehovahQuoteSWitnesses,
  /**
   * Judaism
   */
  Judaism: v3_ReligiousAffiliation_Judaism,
  /**
   * Latter Day Saints
   */
  LatterDaySaints: v3_ReligiousAffiliation_LatterDaySaints,
  /**
   * Lutheran
   */
  Lutheran: v3_ReligiousAffiliation_Lutheran,
  /**
   * Mahayana
   */
  Mahayana: v3_ReligiousAffiliation_Mahayana,
  /**
   * Meditation
   */
  Meditation: v3_ReligiousAffiliation_Meditation,
  /**
   * Messianic Judaism
   */
  MessianicJudaism: v3_ReligiousAffiliation_MessianicJudaism,
  /**
   * Mitraism
   */
  Mitraism: v3_ReligiousAffiliation_Mitraism,
  /**
   * New Age
   */
  NewAge: v3_ReligiousAffiliation_NewAge,
  /**
   * non-Roman Catholic
   */
  NonRomanCatholic: v3_ReligiousAffiliation_NonRomanCatholic,
  /**
   * Occult
   */
  Occult: v3_ReligiousAffiliation_Occult,
  /**
   * Orthodox
   */
  Orthodox: v3_ReligiousAffiliation_Orthodox,
  /**
   * Paganism
   */
  Paganism: v3_ReligiousAffiliation_Paganism,
  /**
   * Pentecostal
   */
  Pentecostal: v3_ReligiousAffiliation_Pentecostal,
  /**
   * Process, The
   */
  ProcessThe: v3_ReligiousAffiliation_ProcessThe,
  /**
   * Reformed/Presbyterian
   */
  ReformedPresbyterian: v3_ReligiousAffiliation_ReformedPresbyterian,
  /**
   * Roman Catholic Church
   */
  RomanCatholicChurch: v3_ReligiousAffiliation_RomanCatholicChurch,
  /**
   * Satanism
   */
  Satanism: v3_ReligiousAffiliation_Satanism,
  /**
   * Scientology
   */
  Scientology: v3_ReligiousAffiliation_Scientology,
  /**
   * Shamanism
   */
  Shamanism: v3_ReligiousAffiliation_Shamanism,
  /**
   * Shiite (Islam)
   */
  ShiiteIslam: v3_ReligiousAffiliation_ShiiteIslam,
  /**
   * Shinto
   */
  Shinto: v3_ReligiousAffiliation_Shinto,
  /**
   * Sikism
   */
  Sikism: v3_ReligiousAffiliation_Sikism,
  /**
   * Spiritualism
   */
  Spiritualism: v3_ReligiousAffiliation_Spiritualism,
  /**
   * Sunni (Islam)
   */
  SunniIslam: v3_ReligiousAffiliation_SunniIslam,
  /**
   * Taoism
   */
  Taoism: v3_ReligiousAffiliation_Taoism,
  /**
   * Theravada
   */
  Theravada: v3_ReligiousAffiliation_Theravada,
  /**
   * Unitarian-Universalism
   */
  UnitarianUniversalism: v3_ReligiousAffiliation_UnitarianUniversalism,
  /**
   * Universal Life Church
   */
  UniversalLifeChurch: v3_ReligiousAffiliation_UniversalLifeChurch,
  /**
   * Vajrayana (Tibetan)
   */
  VajrayanaTibetan: v3_ReligiousAffiliation_VajrayanaTibetan,
  /**
   * Veda
   */
  Veda: v3_ReligiousAffiliation_Veda,
  /**
   * Voodoo
   */
  Voodoo: v3_ReligiousAffiliation_Voodoo,
  /**
   * Wicca
   */
  Wicca: v3_ReligiousAffiliation_Wicca,
  /**
   * Yaohushua
   */
  Yaohushua: v3_ReligiousAffiliation_Yaohushua,
  /**
   * Zen Buddhism
   */
  ZenBuddhism: v3_ReligiousAffiliation_ZenBuddhism,
  /**
   * Zoroastrianism
   */
  Zoroastrianism: v3_ReligiousAffiliation_Zoroastrianism,
  /**
   * Assembly of God
   */
  AssemblyOfGod: v3_ReligiousAffiliation_AssemblyOfGod,
  /**
   * Brethren
   */
  Brethren: v3_ReligiousAffiliation_Brethren,
  /**
   * Christian Scientist
   */
  ChristianScientist: v3_ReligiousAffiliation_ChristianScientist,
  /**
   * Church of Christ
   */
  ChurchOfChrist: v3_ReligiousAffiliation_ChurchOfChrist,
  /**
   * Church of God
   */
  ChurchOfGod: v3_ReligiousAffiliation_ChurchOfGod,
  /**
   * Congregational
   */
  Congregational: v3_ReligiousAffiliation_Congregational,
  /**
   * Disciples of Christ
   */
  DisciplesOfChrist: v3_ReligiousAffiliation_DisciplesOfChrist,
  /**
   * Eastern Orthodox
   */
  EasternOrthodox: v3_ReligiousAffiliation_EasternOrthodox,
  /**
   * Episcopalian
   */
  Episcopalian: v3_ReligiousAffiliation_Episcopalian,
  /**
   * Evangelical Covenant
   */
  EvangelicalCovenant: v3_ReligiousAffiliation_EvangelicalCovenant,
  /**
   * Friends
   */
  Friends: v3_ReligiousAffiliation_Friends,
  /**
   * Full Gospel
   */
  FullGospel: v3_ReligiousAffiliation_FullGospel,
  /**
   * Methodist
   */
  Methodist: v3_ReligiousAffiliation_Methodist,
  /**
   * Native American
   */
  NativeAmerican: v3_ReligiousAffiliation_NativeAmerican,
  /**
   * Nazarene
   */
  Nazarene: v3_ReligiousAffiliation_Nazarene,
  /**
   * Presbyterian
   */
  Presbyterian: v3_ReligiousAffiliation_Presbyterian,
  /**
   * Protestant
   */
  Protestant: v3_ReligiousAffiliation_Protestant,
  /**
   * Protestant, No Denomination
   */
  ProtestantNoDenomination: v3_ReligiousAffiliation_ProtestantNoDenomination,
  /**
   * Reformed
   */
  Reformed: v3_ReligiousAffiliation_Reformed,
  /**
   * Salvation Army
   */
  SalvationArmy: v3_ReligiousAffiliation_SalvationArmy,
  /**
   * Unitarian Universalist
   */
  UnitarianUniversalist: v3_ReligiousAffiliation_UnitarianUniversalist,
  /**
   * United Church of Christ
   */
  UnitedChurchOfChrist: v3_ReligiousAffiliation_UnitedChurchOfChrist,
};
const v3_ResponseLevel_Completion: Coding = {
  code: 'C',
  display: 'completion',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ResponseLevel',
};
const v3_ResponseLevel_Detail: Coding = {
  code: 'D',
  display: 'detail',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ResponseLevel',
};
const v3_ResponseLevel_Exception: Coding = {
  code: 'E',
  display: 'exception',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ResponseLevel',
};
const v3_ResponseLevel_Confirmation: Coding = {
  code: 'F',
  display: 'confirmation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ResponseLevel',
};
const v3_ResponseLevel_MessageControl: Coding = {
  code: 'N',
  display: 'message-control',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ResponseLevel',
};
const v3_ResponseLevel_Modification: Coding = {
  code: 'R',
  display: 'modification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ResponseLevel',
};
const v3_ResponseLevel_None: Coding = {
  code: 'X',
  display: 'none',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ResponseLevel',
};
/**
 *  Specifies whether a response is expected from the addressee of this interaction and what level of detail that response should include
 */
export const V3ResponseLevel = {
  /**
   * Respond with exceptions and a notification of completion
   */
  Completion: v3_ResponseLevel_Completion,
  /**
   * Respond with exceptions, completion, modifications and include more detail information (if applicable)
   */
  Detail: v3_ResponseLevel_Detail,
  /**
   * Respond with exceptions only
   */
  Exception: v3_ResponseLevel_Exception,
  /**
   * Respond with exceptions, completion, and modification with detail (as above), and send positive confirmations even if no modifications are being made.
   */
  Confirmation: v3_ResponseLevel_Confirmation,
  /**
   * Respond only with message level acknowledgements, i.e., only notify acceptance or rejection of the message, do not include any application-level detail
   */
  MessageControl: v3_ResponseLevel_MessageControl,
  /**
   * Respond with exceptions, completions and modifications or revisions done before completion
   */
  Modification: v3_ResponseLevel_Modification,
  /**
   * Do not send any kind of response
   */
  None: v3_ResponseLevel_None,
};
const v3_ResponseModality_Batch: Coding = {
  code: 'B',
  display: 'Batch',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ResponseModality',
};
const v3_ResponseModality_RealTime: Coding = {
  code: 'R',
  display: 'Real Time',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ResponseModality',
};
const v3_ResponseModality_Bolus: Coding = {
  code: 'T',
  display: 'Bolus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ResponseModality',
};
/**
 *  Defines the timing and grouping of the response instances.  OpenIssue:
 * Description copied from Concept Domain of same name.  Must be verified.
 */
export const V3ResponseModality = {
  /**
   * Query response to be sent as an HL7 Batch.
   */
  Batch: v3_ResponseModality_Batch,
  /**
   * Query response to occur in real time.
   */
  RealTime: v3_ResponseModality_RealTime,
  /**
   * Query response to sent as a series of responses at the same time without the use of batch formatting.
   */
  Bolus: v3_ResponseModality_Bolus,
};
const v3_ResponseMode_Deferred: Coding = {
  code: 'D',
  display: 'deferred',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ResponseMode',
};
const v3_ResponseMode_Immediate: Coding = {
  code: 'I',
  display: 'immediate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ResponseMode',
};
const v3_ResponseMode_Queue: Coding = {
  code: 'Q',
  display: 'queue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-ResponseMode',
};
/**
 *  Specifies the mode, immediate versus deferred or queued, by which a receiver should communicate its receiver responsibilities.
 */
export const V3ResponseMode = {
  /**
   * The receiver may respond in a non-immediate manner. Note: this will be the default value.
   */
  Deferred: v3_ResponseMode_Deferred,
  /**
   * The receiver is required to assume that the sender is blocking and behave appropriately by sending an immediate response.
   */
  Immediate: v3_ResponseMode_Immediate,
  /**
   * The receiver shall keep any application responses in a queue until such time as the queue is polled.
   */
  Queue: v3_ResponseMode_Queue,
};
const v3_RoleClass_RoleClassAssociative: Coding = {
  code: '_RoleClassAssociative',
  display: 'RoleClassAssociative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_RoleClassMutualRelationship: Coding = {
  code: '_RoleClassMutualRelationship',
  display: 'RoleClassMutualRelationship',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_RoleClassOntological: Coding = {
  code: '_RoleClassOntological',
  display: 'RoleClassOntological',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_RoleClassPartitive: Coding = {
  code: '_RoleClassPartitive',
  display: 'RoleClassPartitive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_RoleClassPassive: Coding = {
  code: '_RoleClassPassive',
  display: 'RoleClassPassive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_RoleClassRelationshipFormal: Coding = {
  code: '_RoleClassRelationshipFormal',
  display: 'RoleClassRelationshipFormal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Access: Coding = {
  code: 'ACCESS',
  display: 'access',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ActiveIngredient: Coding = {
  code: 'ACTI',
  display: 'active ingredient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ActiveIngredientBasisOfStrength: Coding = {
  code: 'ACTIB',
  display: 'active ingredient - basis of strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ActiveIngredientMoietyIsBasisOfStrength: Coding = {
  code: 'ACTIM',
  display: 'active ingredient - moiety is basis of strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ActiveIngredientReferenceSubstanceIsBasisOfStrength: Coding = {
  code: 'ACTIR',
  display: 'active ingredient - reference substance is basis of strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ActiveMoiety: Coding = {
  code: 'ACTM',
  display: 'active moiety',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Adjuvant: Coding = {
  code: 'ADJV',
  display: 'adjuvant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Adjacency: Coding = {
  code: 'ADJY',
  display: 'adjacency',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_AdministerableMaterial: Coding = {
  code: 'ADMM',
  display: 'Administerable Material',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Additive: Coding = {
  code: 'ADTV',
  display: 'additive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Aliquot: Coding = {
  code: 'ALQT',
  display: 'aliquot',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Base: Coding = {
  code: 'BASE',
  display: 'base',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Birthplace: Coding = {
  code: 'BIRTHPL',
  display: 'birthplace',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_MolecularBond: Coding = {
  code: 'BOND',
  display: 'molecular bond',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Caregiver: Coding = {
  code: 'CAREGIVER',
  display: 'caregiver',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_CaseSubject: Coding = {
  code: 'CASEBJ',
  display: 'Case Subject',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Child: Coding = {
  code: 'CHILD',
  display: 'child',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Citizen: Coding = {
  code: 'CIT',
  display: 'citizen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ContaminantIngredient: Coding = {
  code: 'CNTM',
  display: 'contaminant ingredient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ColorAdditive: Coding = {
  code: 'COLR',
  display: 'color additive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_CommissioningParty: Coding = {
  code: 'COMPAR',
  display: 'commissioning party',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Contact: Coding = {
  code: 'CON',
  display: 'contact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Connection: Coding = {
  code: 'CONC',
  display: 'connection',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Content: Coding = {
  code: 'CONT',
  display: 'content',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Continuity: Coding = {
  code: 'CONY',
  display: 'continuity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_CredentialedEntity: Coding = {
  code: 'CRED',
  display: 'credentialed entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ClinicalResearchInvestigator: Coding = {
  code: 'CRINV',
  display: 'clinical research investigator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ClinicalResearchSponsor: Coding = {
  code: 'CRSPNSR',
  display: 'clinical research sponsor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_PlaceOfDeath: Coding = {
  code: 'DEATHPLC',
  display: 'place of death',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_DedicatedServiceDeliveryLocation: Coding = {
  code: 'DSDLOC',
  display: 'dedicated service delivery location',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_DistributedMaterial: Coding = {
  code: 'DST',
  display: 'distributed material',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_EquivalentEntity: Coding = {
  code: 'EQUIV',
  display: 'equivalent entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_EventLocation: Coding = {
  code: 'EXLOC',
  display: 'event location',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ExposureAgentCarrier: Coding = {
  code: 'EXPAGTCAR',
  display: 'exposure agent carrier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ExposedEntity: Coding = {
  code: 'EXPR',
  display: 'exposed entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ExposureVector: Coding = {
  code: 'EXPVECTOR',
  display: 'exposure vector',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_FlavorAdditive: Coding = {
  code: 'FLVR',
  display: 'flavor additive',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Fomite: Coding = {
  code: 'FOMITE',
  display: 'fomite',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_HasGeneralization: Coding = {
  code: 'GEN',
  display: 'has generalization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_HasGeneric: Coding = {
  code: 'GRIC',
  display: 'has generic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Guarantor: Coding = {
  code: 'GUAR',
  display: 'guarantor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_HeldEntity: Coding = {
  code: 'HLD',
  display: 'held entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_HealthChart: Coding = {
  code: 'HLTHCHRT',
  display: 'health chart',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_InactiveIngredient: Coding = {
  code: 'IACT',
  display: 'inactive ingredient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_IdentifiedEntity: Coding = {
  code: 'IDENT',
  display: 'identified entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Individual: Coding = {
  code: 'INDIV',
  display: 'individual',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Ingredient: Coding = {
  code: 'INGR',
  display: 'ingredient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Instance: Coding = {
  code: 'INST',
  display: 'instance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_IncidentalServiceDeliveryLocation: Coding = {
  code: 'ISDLOC',
  display: 'incidental service delivery location',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Isolate: Coding = {
  code: 'ISLT',
  display: 'isolate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_LicensedEntity: Coding = {
  code: 'LIC',
  display: 'licensed entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_LocatedEntity: Coding = {
  code: 'LOCE',
  display: 'located entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ManufacturedProduct: Coding = {
  code: 'MANU',
  display: 'manufactured product',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Member: Coding = {
  code: 'MBR',
  display: 'member',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_MechanicalIngredient: Coding = {
  code: 'MECH',
  display: 'mechanical ingredient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_MilitaryPerson: Coding = {
  code: 'MIL',
  display: 'military person',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_MaintainedEntity: Coding = {
  code: 'MNT',
  display: 'maintained entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_NursePractitioner: Coding = {
  code: 'NURPRAC',
  display: 'nurse practitioner',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Nurse: Coding = {
  code: 'NURS',
  display: 'nurse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_OwnedEntity: Coding = {
  code: 'OWN',
  display: 'owned entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_PhysicianAssistant: Coding = {
  code: 'PA',
  display: 'physician assistant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Part: Coding = {
  code: 'PART',
  display: 'part',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Payee: Coding = {
  code: 'PAYEE',
  display: 'payee',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_InvoicePayor: Coding = {
  code: 'PAYOR',
  display: 'invoice payor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Physician: Coding = {
  code: 'PHYS',
  display: 'physician',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_PolicyHolder: Coding = {
  code: 'POLHOLD',
  display: 'policy holder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ProgramEligible: Coding = {
  code: 'PROG',
  display: 'program eligible',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_PersonalRelationship: Coding = {
  code: 'PRS',
  display: 'personal relationship',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Preservative: Coding = {
  code: 'PRSV',
  display: 'preservative',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_QualifiedEntity: Coding = {
  code: 'QUAL',
  display: 'qualified entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ResearchSubject: Coding = {
  code: 'RESBJ',
  display: 'research subject',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_RetailedMaterial: Coding = {
  code: 'RET',
  display: 'retailed material',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_RegulatedProduct: Coding = {
  code: 'RGPR',
  display: 'regulated product',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Role: Coding = {
  code: 'ROL',
  display: 'role',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Same: Coding = {
  code: 'SAME',
  display: 'same',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_ServiceDeliveryLocation: Coding = {
  code: 'SDLOC',
  display: 'service delivery location',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Self: Coding = {
  code: 'SELF',
  display: 'self',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_SigningAuthorityOrOfficer: Coding = {
  code: 'SGNOFF',
  display: 'signing authority or officer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Specimen: Coding = {
  code: 'SPEC',
  display: 'specimen',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_CoverageSponsor: Coding = {
  code: 'SPNSR',
  display: 'coverage sponsor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Stabilizer: Coding = {
  code: 'STBL',
  display: 'stabilizer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Student: Coding = {
  code: 'STD',
  display: 'student',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_StoredEntity: Coding = {
  code: 'STOR',
  display: 'stored entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Subsumer: Coding = {
  code: 'SUBS',
  display: 'subsumer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Subscriber: Coding = {
  code: 'SUBSCR',
  display: 'subscriber',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_SubsumedBy: Coding = {
  code: 'SUBY',
  display: 'subsumed by',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_TerritoryOfAuthority: Coding = {
  code: 'TERR',
  display: 'territory of authority',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_TherapeuticAgent: Coding = {
  code: 'THER',
  display: 'therapeutic agent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_Underwriter: Coding = {
  code: 'UNDWRT',
  display: 'underwriter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_UsedEntity: Coding = {
  code: 'USED',
  display: 'used entity',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
const v3_RoleClass_WarrantedProduct: Coding = {
  code: 'WRTE',
  display: 'warranted product',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleClass',
};
/**
 *  Codes for the Role class hierarchy.  The values in this hierarchy, represent a Role which is an association or relationship between two entities - the entity that plays the role and the entity that scopes the role.  Roles names are derived from the name of the playing entity in that role. The role hierarchy stems from three core concepts, or abstract domains:    RoleClassOntological
 * is an abstract domain that collects roles in which the playing entity is defined or specified by the scoping entity.   RoleClassPartitive
 * collects roles in which the playing entity is in some sense a "part" of the scoping entity.   RoleClassAssociative
 * collects all of the remaining forms of association between the playing entity and the scoping entity. This set of roles is further partitioned between:    RoleClassPassive
 * which are roles in which the playing entity is used, known, treated, handled, built, or destroyed, etc. under the auspices of the scoping entity. The playing entity is passive in these roles in that the role exists without an agreement from the playing entity.   RoleClassMutualRelationship
 * which are relationships based on mutual behavior of the two entities. The basis of these relationship may be formal agreements or they may bede facto
 * behavior.  Thus, this sub-domain is further divided into:    RoleClassRelationshipFormal
 * in which the relationship is formally defined, frequently by a contract or agreement.   Personal relationship
 * which inks two people in a personal relationship. The hierarchy discussed above is represented In the current vocabulary tables as a set of abstract domains, with the exception of the  "Personal relationship" which is a leaf concept.  OpenIssue:
 * Description copied from Concept Domain of same name.  Must be verified.
 */
export const V3RoleClass = {
  /**
   * A general association between two entities that is neither partitive nor ontological.
   */
  RoleClassAssociative: v3_RoleClass_RoleClassAssociative,
  /**
   * A relationship that is based on mutual behavior of the two Entities as being related. The basis of such relationship may be agreements (e.g., spouses, contract parties) or they may be de facto behavior (e.g. friends) or may be an incidental involvement with each other (e.g. parties over a dispute, siblings, children).
   */
  RoleClassMutualRelationship: v3_RoleClass_RoleClassMutualRelationship,
  /**
   * A relationship in which the scoping Entity defines or specifies what the playing Entity is.  Thus, the player's "being" (Greek: ontos) is specified.
   */
  RoleClassOntological: v3_RoleClass_RoleClassOntological,
  /**
   * An association between two Entities where the playing Entity is considered in some way "part" of the scoping Entity, e.g., as a member, component, ingredient, or content. Being "part" in the broadest sense of the word can mean anything from being an integral structural component to a mere incidental temporary association of a playing Entity with a (generally larger) scoping Entity.
   */
  RoleClassPartitive: v3_RoleClass_RoleClassPartitive,
  /**
   * An association for a playing Entity that is used, known, treated, handled, built, or destroyed, etc. under the auspices of the scoping Entity. The playing Entity is passive in these roles (even though it may be active in other roles), in the sense that the kinds of things done to it in this role happen without an agreement from the playing Entity.
   */
  RoleClassPassive: v3_RoleClass_RoleClassPassive,
  /**
   * A relationship between two entities that is formally recognized, frequently by a contract or similar agreement.
   */
  RoleClassRelationshipFormal: v3_RoleClass_RoleClassRelationshipFormal,
  /**
   * A role in which the playing entity (material) provides access to another entity. The principal use case is intravenous (or other bodily) access lines that preexist and need to be referred to for medication routing instructions.
   */
  Access: v3_RoleClass_Access,
  /**
   * Definition: a therapeutically active ingredient (player) in a mixture (scoper), where the mixture is typically a manufactured pharmaceutical.  It is unknown if the quantity of such an ingredient is expressed precisely in terms of the playing ingredient substance, or, if it is specified in terms of a closely related substance (active moiety or reference substance).
   */
  ActiveIngredient: v3_RoleClass_ActiveIngredient,
  /**
   * Description:  Active ingredient, where the ingredient substance (player) is itself the "basis of strength", i.e., where the Role.quantity specifies exactly the quantity of the player substance in the medicine formulation.
   *
   *
   *                            Examples: Lopressor 50 mg actually contains 50 mg of metoprolol succinate, even though the active moiety is metoprolol,  but also: Tenormin 50 mg contain 50 mg of atenolol, as free base, i.e., where the active ingredient atenolol is also the active moiety.
   */
  ActiveIngredientBasisOfStrength: v3_RoleClass_ActiveIngredientBasisOfStrength,
  /**
   * Description: Active ingredient, where not the ingredient substance (player), but itaTMs active moiety is the "basis of strength", i.e., where the Role.quantity specifies the quantity of the player substance's active moiety in the medicine formulation.
   *
   *
   *                            Examples: 1 mL of Betopic 5mg/mL eye drops contains 5.6 mg betaxolol hydrochloride equivalent to betaxolol base 5 mg.
   */
  ActiveIngredientMoietyIsBasisOfStrength: v3_RoleClass_ActiveIngredientMoietyIsBasisOfStrength,
  /**
   * Description: Active ingredient, where not the ingredient substance (player) but another reference substance with the same active moiety, is the "basis of strength", i.e., where the Role.quantity specifies the quantity of a reference substance, similar but different from the player substance's in the medicine formulation.
   *
   *
   *                            Examples: Toprol-XL 50 mg contains 47.5 mg of metoprolol succinate equivalent to 50 mg of metoprolol tartrate.
   */
  ActiveIngredientReferenceSubstanceIsBasisOfStrength: v3_RoleClass_ActiveIngredientReferenceSubstanceIsBasisOfStrength,
  /**
   * The molecule or ion that is responsible for the intended pharmacological action of the drug substance, excluding those appended or associated parts of the molecule that make the molecule an ester, salt (including a salt with hydrogen or coordination bonds), or other noncovalent derivative (such as a complex, chelate, or clathrate).
   *
   *                         Examples: heparin-sodium and heparin-potassium have the same active moiety, heparin; the active moiety of morphine-hydrochloride is morphine.
   */
  ActiveMoiety: v3_RoleClass_ActiveMoiety,
  /**
   * A component (player) added to enhance the action of an active ingredient (scoper) (in the manner of a catalyst) but which has no active effect in and of itself.  Such ingredients are significant in defining equivalence of products in a way that inactive ingredients are not.
   */
  Adjuvant: v3_RoleClass_Adjuvant,
  /**
   * A physical association whereby two Entities are in some (even lose) spatial relationship with each other such that they touch each other in some way.
   *
   *
   *                            Examples: the colon is connected (and therefore adjacent) to the jejunum; the colon is adjacent to the liver (even if not actually connected.)
   *
   *
   *                            UsageConstraints: Adjacency is in principle a symmetrical connection, but scoper and player of the role should, where applicable, be assigned to have scoper be the larger, more central Entity and player the smaller, more distant, appendage.
   */
  Adjacency: v3_RoleClass_Adjacency,
  /**
   * A material (player) that can be administered to an Entity (scoper).
   */
  AdministerableMaterial: v3_RoleClass_AdministerableMaterial,
  /**
   * An ingredient (player)  that is added to a base (scoper), that amounts to a minor part of the overall mixture.
   */
  Additive: v3_RoleClass_Additive,
  /**
   * Player of the Affiliate role has a business/professional relationship with scoper.  Player and scoper may be persons or organization.  The Affiliate relationship does not imply membership in a group, nor does it exist for resource scheduling purposes.
   *
   *
   *                            Example: A healthcare provider is affiliated with another provider as a business associate.
   */
  Affiliate: v3_RoleClass_Affiliate,
  /**
   * An entity (player) that acts or is authorized to act on behalf of another entity (scoper).
   */
  Agent: v3_RoleClass_Agent,
  /**
   * A portion (player) of an original or source specimen (scoper) used for testing or transportation.
   */
  Aliquot: v3_RoleClass_Aliquot,
  /**
   * An agent role in which the agent is an Entity acting in the employ of an organization.  The focus is on functional role on behalf of the organization, unlike the Employee role where the focus is on the 'Human Resources' relationship between the employee and the organization.
   */
  AssignedEntity: v3_RoleClass_AssignedEntity,
  /**
   * A base ingredient (player) is what comprises the major part of a mixture (scoper). E.g., Water in most i.v. solutions, or Vaseline in salves. Among all ingredients of a material, there should be only one base. A base substance can, in turn, be a mixture.
   */
  Base: v3_RoleClass_Base,
  /**
   * Relates a place (playing Entity) as the location where a living subject (scoping Entity) was born.
   */
  Birthplace: v3_RoleClass_Birthplace,
  /**
   * A connection between two atoms of a molecule.
   *
   *
   *                            Examples: double bond between first and second C in ethane, peptide bond between two amino-acid, disulfide bridge between two proteins, chelate and ion associations, even the much weaker van-der-Waals bonds can be considered molecular bonds.
   *
   *
   *                            UsageConstraints: See connection and adjacency for the assignment of player and scoper.
   */
  MolecularBond: v3_RoleClass_MolecularBond,
  /**
   * A person responsible for the primary care of a patient at home.
   */
  Caregiver: v3_RoleClass_Caregiver,
  /**
   * A person, non-person living subject, or place that is the subject of an investigation related to a notifiable condition (health circumstance that is reportable within the applicable public health jurisdiction)
   */
  CaseSubject: v3_RoleClass_CaseSubject,
  /**
   * The player of the role is a child of the scoping entity, in a generic sense.
   */
  Child: v3_RoleClass_Child,
  /**
   * Citizen of apolitical entity
   */
  Citizen: v3_RoleClass_Citizen,
  /**
   * Description: A role played by a party making a claim for coverage under a policy or program.  A claimant must be either a person or organization, or a group of persons or organizations.  A claimant is not a named insured or a program eligible.
   *
   *
   *                            Discussion: With respect to liability insurance such as property and casualty insurance, a claimant must file a claim requesting indemnification for a loss that the claimant considers covered under the policy of a named insured.  The claims adjuster for the policy underwriter will review the claim to determine whether the loss meets the benefit coverage criteria under a policy, and base any indemnification or coverage payment on that review.  If a third party is liable in whole or part for the loss, the underwriter may pursue third party liability recovery.  A claimant may be involved in civil or criminal legal proceedings involving claims against a defendant party that is indemnified by an insurance policy or to protest the finding of a claims adjustor. With respect to life insurance, a beneficiary designated by a named insured becomes a claimant of the proceeds of coverage, as in the case of a life insurance policy.  However, a claimant for coverage under life insurance is not necessarily a designated beneficiary.
   *
   *
   *                            Note: A claimant is not a named insured.  However, a named insured may make a claim under a policy, e.g., an insured driver may make a claim for an injury under his or her comprehensive automobile insurance policy.  Similarly, a program eligible may make a claim under program, e.g., an unemployed worker may claim benefits under an unemployment insurance program, but parties playing these covered party role classes are not, for purposes of this vocabulary and in an effort to clearly distinguish role classes, considered claimants.
   *
   *                         In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that either a named insured or an individual insured has filed a claim for a loss.  In the case of a program eligible, a role type code INJWKR (injured worker) subtypes the class to indicate that the covered party in a workers compensation program is an injured worker, and as such, has filed a "claim" under the program for benefits.  Likewise, a covered role type code UNEMP (unemployed worker) subtypes the program eligible class to indicate that the covered party in an unemployment insurance program has filed a claim for unemployment benefits.
   *
   *
   *                            Example: A claimant under automobile policy that is not the named insured.
   */
  Claimant: v3_RoleClass_Claimant,
  /**
   * An ingredient whose presence is not intended but may not be reasonably avoided given the circumstances of the mixture's nature or origin.
   */
  ContaminantIngredient: v3_RoleClass_ContaminantIngredient,
  /**
   * A substance (player) influencing the optical aspect of material (scoper).
   */
  ColorAdditive: v3_RoleClass_ColorAdditive,
  /**
   * An Entity that is authorized to issue or instantiate permissions, privileges, credentials or other formal/legal authorizations.
   */
  CommissioningParty: v3_RoleClass_CommissioningParty,
  /**
   * A person or an organization (player) which provides or receives information regarding another entity (scoper).  Examples; patient NOK and emergency contacts; guarantor contact; employer contact.
   */
  Contact: v3_RoleClass_Contact,
  /**
   * An adjacency of two Entities held together by a bond which attaches to each of the two entities.
   *
   *
   *                            Examples: biceps brachii muscle connected to the radius bone, port 3 on a network switch connected to port 5 on a patch panel.
   *
   *
   *                            UsageConstraints: See Adjacency for the assignment of scoper (larger, more central) and player (smaller, more distant).
   */
  Connection: v3_RoleClass_Connection,
  /**
   * Relates a material as the content (player) to a container (scoper).  Unlike ingredients, the content and a container remain separate (not mixed) and the content can be removed from the container.  A content is not part of an empty container.
   */
  Content: v3_RoleClass_Content,
  /**
   * A connection between two regional parts.
   *
   *
   *                            Examples:  the connection between ascending aorta and the aortic arc, connection between descending colon and sigmoid.
   *
   *
   *                            UsageConstraints: See connection and adjacency for the assignment of player and scoper.
   */
  Continuity: v3_RoleClass_Continuity,
  /**
   * A role class played by a person who receives benefit coverage under the terms of a particular insurance policy.  The underwriter of that policy is the scoping entity.  The covered party receives coverage because of some contractual or other relationship with the holder of that policy.
   *
   *
   *                            Discussion:This reason for coverage is captured in 'Role.code' and a relationship link with type code of indirect authority should be included using the policy holder role as the source, and the covered party role as the target.
   *
   *                         Note that a particular policy may cover several individuals one of whom may be, but need not be, the policy holder.  Thus the notion of covered party is a role that is distinct from that of the policy holder.
   */
  CoveredParty: v3_RoleClass_CoveredParty,
  /**
   * A role played by an entity that receives credentials from the scoping entity.
   */
  CredentialedEntity: v3_RoleClass_CredentialedEntity,
  /**
   * A role played by a provider, always a person, who has agency authority from a Clinical Research Sponsor to direct the conduct of a clinical research trial or study on behalf of the sponsor.
   */
  ClinicalResearchInvestigator: v3_RoleClass_ClinicalResearchInvestigator,
  /**
   * A role played by an entity, usually an organization, that is the sponsor of a clinical research trial or study.  The sponsor commissions the study, bears the expenses, is responsible for satisfying all legal requirements concerning subject safety and privacy, and is generally responsible for collection, storage and analysis of the data generated during the trial.  No scoper is necessary, as a clinical research sponsor undertakes the role on its own authority and declaration. Clinical research sponsors are usually educational or other research organizations, government agencies or biopharmaceutical companies.
   */
  ClinicalResearchSponsor: v3_RoleClass_ClinicalResearchSponsor,
  /**
   * Definition: Relates a place (playing Entity) as the location where a living subject (scoping Entity) died.
   */
  PlaceOfDeath: v3_RoleClass_PlaceOfDeath,
  /**
   * Description: A role played by a person covered under a policy or program based on an association with a subscriber, which is recognized by the policy holder.
   *
   *
   *                            Note:  The party playing the role of a dependent is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a dependent may make a claim under a policy, e.g., a dependent under a health insurance policy may become the claimant for coverage under that policy for wellness examines or if injured and there is no liable third party.  In the case of a dependent making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that the dependent has filed a claim for services covered under the health insurance policy.
   *
   *
   *                            Example: The dependent has an association with the subscriber such as a financial dependency or personal relationship such as that of a spouse, or a natural or adopted child.  The policy holder may be required by law to recognize certain associations or may have discretion about the associations.  For example, a policy holder may dictate the criteria for the dependent status of adult children who are students, such as requiring full time enrollment, or may recognize domestic partners as dependents.  Under certain circumstances, the dependent may be under the indirect authority of a responsible party acting as a surrogate for the subscriber, for example, if the subscriber is differently abled or deceased, a guardian ad Lidem or estate executor may be appointed to assume the subscriberaTMs legal standing in the relationship with the dependent.
   */
  Dependent: v3_RoleClass_Dependent,
  /**
   * A role of a place (player) that is intended to house the provision of services. Scoper is the Entity (typically Organization) that provides these services. This is not synonymous with "ownership."
   */
  DedicatedServiceDeliveryLocation: v3_RoleClass_DedicatedServiceDeliveryLocation,
  /**
   * A material (player) distributed by a distributor (scoper) who functions between a manufacturer and a buyer or retailer.
   */
  DistributedMaterial: v3_RoleClass_DistributedMaterial,
  /**
   * An entity to be contacted in the event of an emergency.
   */
  EmergencyContact: v3_RoleClass_EmergencyContact,
  /**
   * A relationship between a person or organization and a person or organization formed for the purpose of exchanging work for compensation.  The purpose of the role is to identify the type of relationship the employee has to the employer, rather than the nature of the work actually performed.  (Contrast with AssignedEntity.)
   */
  Employee: v3_RoleClass_Employee,
  /**
   * Description: Specifies the player Entity (the equivalent Entity) as an Entity that is considered to be equivalent to a reference Entity (scoper).  The equivalence is in principle a symmetric relationship, however, it is expected that the scoper is a reference entity which serves as reference entity for multiple different equivalent entities.
   *
   *
   *                            Examples: An innovator's medicine formulation is the reference for "generics", i.e., formulations manufactured differently but having been proven to be biologically equivalent to the reference medicine. Another example is a reference ingredient that serves as basis for quantity specifications (basis of strength, e.g., metoprolol succinate specified in terms of metoprolol tartrate.)
   */
  EquivalentEntity: v3_RoleClass_EquivalentEntity,
  /**
   * A role played by a place at which the location of an event may be recorded.
   */
  EventLocation: v3_RoleClass_EventLocation,
  /**
   * An exposure agent carrier is an entity that is capable of conveying an exposure agent from one entity to another.  The scoper of the role must be the exposure agent (e.g., pathogen).
   */
  ExposureAgentCarrier: v3_RoleClass_ExposureAgentCarrier,
  /**
   * A role played by an entity that has been exposed to a person or animal suffering a contagious disease, or with a location from which a toxin has been distributed.  The player of the role is normally a person or animal, but it is possible that other entity types could become exposed.  The role is scoped by the source of the exposure, and it is quite possible for a person playing the role of exposed party to also become the scoper a role played by another person.  That is to say, once a person has become infected, it is possible, perhaps likely, for that person to infect others.
   *
   *                         Management of exposures and tracking exposed parties is a key function within public health, and within most public health contexts - exposed parties are known as "contacts."
   */
  ExposedEntity: v3_RoleClass_ExposedEntity,
  /**
   * Description: A vector is a living subject that carries an exposure agent.  The vector does not cause the disease itself, but exposes targets to the exposure agent.  A mosquito carrying malaria is an example of a vector.  The scoper of the role must be the exposure agent (e.g., pathogen).
   */
  ExposureVector: v3_RoleClass_ExposureVector,
  /**
   * A substance (player) added to a mixture (scoper) to make it taste a certain way.  In food the use is obvious, in pharmaceuticals flavors can hide disgusting taste of the active ingredient (important in pediatric treatments).
   */
  FlavorAdditive: v3_RoleClass_FlavorAdditive,
  /**
   * Description: A fomite is a non-living entity that is capable of conveying exposure agent from one entity to another.  A doorknob contaminated with a Norovirus is an example of a fomite.  Anyone touching the doorknob would be exposed to the virus.  The scoper of the role must be the exposure agent (e.g., pathogen).
   */
  Fomite: v3_RoleClass_Fomite,
  /**
   * Relates a specialized material concept (player) to its generalization (scoper).
   */
  HasGeneralization: v3_RoleClass_HasGeneralization,
  /**
   * A special link between pharmaceuticals indicating that the target (scoper) is a generic for the source (player).
   */
  HasGeneric: v3_RoleClass_HasGeneric,
  /**
   * A person or organization (player) that serves as a financial guarantor for another person or organization (scoper).
   */
  Guarantor: v3_RoleClass_Guarantor,
  /**
   * Guardian of a ward
   */
  Guardian: v3_RoleClass_Guardian,
  /**
   * Entity that is currently in the possession of a holder (scoper), who holds, or uses it, usually based on some agreement with the owner.
   */
  HeldEntity: v3_RoleClass_HeldEntity,
  /**
   * The role of a material (player) that is the physical health chart belonging to an organization (scoper).
   */
  HealthChart: v3_RoleClass_HealthChart,
  /**
   * An ingredient which is not considered therapeutically active, e.g., colors, flavors, stabilizers, or preservatives, fillers, or structural components added to an active ingredient in order to facilitate administration of the active ingredient but without being considered therapeutically active. An inactive ingredient need not be biologically inert, e.g., might be active as an allergen or might have a pleasant taste, but is not an essential constituent delivering the therapeutic effect.
   */
  InactiveIngredient: v3_RoleClass_InactiveIngredient,
  /**
   * A role in which the scoping entity designates an identifier for a playing entity.
   */
  IdentifiedEntity: v3_RoleClass_IdentifiedEntity,
  /**
   * Description: A role played by a party covered under a policy as the policy holder.  An individual may be either a person or an organization.
   *
   *
   *                            Note: The party playing the role of an individual insured is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a named insured may make a claim under a policy, e.g., a party that is the named insured and policy holder under a comprehensive automobile insurance policy may become the claimant for coverage under that policy if injured in an automobile accident and there is no liable third party.  In the case of an individual insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that an individual insured has filed a claim for a loss.
   *
   *
   *                            Example: The individual insured under a comprehensive automobile, disability, or property and casualty policy that is the policy holder.
   */
  Individual: v3_RoleClass_Individual,
  /**
   * Relates a component (player) to a mixture (scoper). E.g., Glucose and Water are ingredients of D5W, latex may be an ingredient in a tracheal tube.
   */
  Ingredient: v3_RoleClass_Ingredient,
  /**
   * An individual piece of material (player) instantiating a class of material (scoper).
   */
  Instance: v3_RoleClass_Instance,
  /**
   * An entity that is the subject of an investigation. This role is scoped by the party responsible for the investigation.
   */
  InvestigationSubject: v3_RoleClass_InvestigationSubject,
  /**
   * A role played by a place at which health care services may be provided without prior designation or authorization.
   */
  IncidentalServiceDeliveryLocation: v3_RoleClass_IncidentalServiceDeliveryLocation,
  /**
   * A microorganism that has been isolated from other microorganisms or a source matrix.
   */
  Isolate: v3_RoleClass_Isolate,
  /**
   * A relationship in which the scoper certifies the player ( e. g. a medical care giver, a medical device or a provider organization) to perform certain activities that fall under the jurisdiction of the scoper (e.g., a health authority licensing healthcare providers, a medical quality authority certifying healthcare professionals).
   */
  LicensedEntity: v3_RoleClass_LicensedEntity,
  /**
   * Relates an entity (player) to a location (scoper) at which it is present in some way. This presence may be limited in time.
   */
  LocatedEntity: v3_RoleClass_LocatedEntity,
  /**
   * Scoped by the manufacturer
   */
  ManufacturedProduct: v3_RoleClass_ManufacturedProduct,
  /**
   * A role played by an entity that is a member of a group.  The group provides the scope for this role.
   *
   *                         Among other uses, groups as used in insurance (groups of covered individuals) and in scheduling where resources may be grouped for scheduling and logistical purposes.
   */
  Member: v3_RoleClass_Member,
  /**
   * An ingredient (player) of a medication (scoper) that is inseparable from the active ingredients, but has no intended chemical or pharmaceutical effect itself, but which may have some systemic effect on the patient.
   *
   *                         An example is a collagen matrix used as a base for transplanting skin cells.  The collagen matrix can be left permanently in the graft site.  Because it is of bovine origin, the patient may exhibit allergies or may have cultural objections to its use.
   */
  MechanicalIngredient: v3_RoleClass_MechanicalIngredient,
  /**
   * A role played by a member of a military service. Scoper is the military service (e.g. Army, Navy, Air Force, etc.) or, more specifically, the unit (e.g. Company C, 3rd Battalion, 4th Division, etc.)
   */
  MilitaryPerson: v3_RoleClass_MilitaryPerson,
  /**
   * An entity (player) that is maintained by another entity (scoper).  This is typical role held by durable equipment. The scoper assumes responsibility for proper operation, quality, and safety.
   */
  MaintainedEntity: v3_RoleClass_MaintainedEntity,
  /**
   * Description: A role played by a party to an insurance policy to which the insurer agrees to indemnify for losses, provides benefits for, or renders services.  A named insured may be either a person, non-person living subject, or an organization, or a group of persons, non-person living subjects, or organizations.
   *
   *
   *                            Discussion: The coded concept NAMED should not be used where a more specific child concept in this Specializable value set applies.  In some cases, the named insured may not be the policy holder, e.g., where a policy holder purchases life insurance policy in which another party is the named insured and the policy holder is the beneficiary of the policy.
   *
   *
   *                            Note: The party playing the role of a named insured is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a named insured may make a claim under a policy, e.g., e.g., a party that is the named insured and policy holder under a comprehensive automobile insurance policy may become the claimant for coverage under that policy e.g., if injured in an automobile accident and there is no liable third party.  In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that a named insured has filed a claim for a loss.
   *
   *
   *                            Example: The named insured under a comprehensive automobile, disability, or property and casualty policy that is the named insured and may or may not be the policy holder.
   */
  NamedInsured: v3_RoleClass_NamedInsured,
  /**
   * An individual designated for notification as the next of kin for a given entity.
   */
  NextOfKin: v3_RoleClass_NextOfKin,
  /**
   * notary public
   */
  NotaryPublic: v3_RoleClass_NotaryPublic,
  /**
   * nurse practitioner
   */
  NursePractitioner: v3_RoleClass_NursePractitioner,
  /**
   * nurse
   */
  Nurse: v3_RoleClass_Nurse,
  /**
   * An Entity (player) for which someone (scoper) is granted by law the right to call the material (player) his own.  This entitles the scoper to make decisions about the disposition of that material.
   */
  OwnedEntity: v3_RoleClass_OwnedEntity,
  /**
   * physician assistant
   */
  PhysicianAssistant: v3_RoleClass_PhysicianAssistant,
  /**
   * Definition:  an association between two Entities where the playing Entity (the part) is a component of the whole (scoper) in the sense of an integral structural component, that is distinct from other parts in the same whole, has a distinct function in the whole, and, as an effect, the full integrity of the whole depends (to some degree) on the presence of this part, even though the part may often be separable from the whole.
   *
   *
   *                            Discussion: Part is defined in opposition to (a) ingredient (not separable), (b) content (not a functional component), and (c) member (not functionally distinct from other members).
   */
  Part: v3_RoleClass_Part,
  /**
   * A Role of a LivingSubject (player) as an actual or potential recipient of health care services from a healthcare provider organization (scoper).
   *
   *
   *                            Usage Note: Communication about relationships between patients and specific healthcare practitioners (people) is not done via scoper.  Instead this is generally done using the CareProvision act.  This allows linkage between patient and a particular healthcare practitioner role and also allows description of the type of care involved in the relationship.
   */
  Patient: v3_RoleClass_Patient,
  /**
   * The role of an organization or individual designated to receive payment for a claim against a particular coverage. The scoping entity is the organization that is the submitter of the invoice in question.
   */
  Payee: v3_RoleClass_Payee,
  /**
   * The role of an organization that undertakes to accept claims invoices, assess the coverage or payments due for those invoices and pay to the designated payees for those invoices.  This role may be either the underwriter or a third-party organization authorized by the underwriter.  The scoping entity is the organization that underwrites the claimed coverage.
   */
  InvoicePayor: v3_RoleClass_InvoicePayor,
  /**
   * physician
   */
  Physician: v3_RoleClass_Physician,
  /**
   * A role played by a person or organization that holds an insurance policy.  The underwriter of that policy is the scoping entity.
   *
   *
   *                            Discussion:The identifier of the policy is captured in 'Role.id' when the Role is a policy holder.
   *
   *                         A particular policy may cover several individuals one of whom may be, but need not be, the policy holder.  Thus the notion of covered party is a role that is distinct from that of the policy holder.
   */
  PolicyHolder: v3_RoleClass_PolicyHolder,
  /**
   * Description: A role played by a party that meets the eligibility criteria for coverage under a program.  A program eligible may be either a person, non-person living subject, or an organization, or a group of persons, non-person living subjects, or organizations.
   *
   *
   *                            Discussion: A program as typically government administered coverage for parties determined eligible under the terms of the program.
   *
   *
   *                            Note: The party playing a program eligible is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However a program eligible may make a claim under program, e.g., an unemployed worker may claim benefits under an unemployment insurance program, but parties playing these covered party role classes are not, for purposes of this vocabulary and in an effort to clearly distinguish role classes, considered claimants.
   *
   *                         In the case of a program eligible, a role type code INJWKR (injured worker) subtypes the class to indicate that the covered party in a workers compensation program is an injured worker, and as such, has filed a "claim" under the program for benefits.  Likewise, a covered role type code UNEMP (unemployed worker) subtypes the program eligible class to indicate that the covered party in an unemployment insurance program has filed a claim for unemployment benefits.
   *
   *
   *                            Example: A party meeting eligibility criteria related to health or financial status, e.g., in the U.S., persons meeting health, demographic, or financial criteria established by state and federal law are eligible for Medicaid.
   */
  ProgramEligible: v3_RoleClass_ProgramEligible,
  /**
   * An Entity (player) that is authorized to provide health care services by some authorizing agency (scoper).
   */
  HealthcareProvider: v3_RoleClass_HealthcareProvider,
  /**
   * Links two entities with classCode PSN (person) in a personal relationship. The character of the relationship must be defined by a PersonalRelationshipRoleType code. The player and scoper are determined by PersonalRelationshipRoleType code as well.
   */
  PersonalRelationship: v3_RoleClass_PersonalRelationship,
  /**
   * A substance (player) added to a mixture (scoper) to prevent microorganisms (fungi, bacteria) to spoil the mixture.
   */
  Preservative: v3_RoleClass_Preservative,
  /**
   * An entity (player) that has been recognized as having certain training/experience or other characteristics that would make said entity an appropriate performer for a certain activity. The scoper is an organization that educates or qualifies entities.
   */
  QualifiedEntity: v3_RoleClass_QualifiedEntity,
  /**
   * Definition:Specifies the administrative functionality within a formal experimental design for which the ResearchSubject role was established.
   *
   *
   *                            Examples: Screening - role is used for pre-enrollment evaluation portion of the design; enrolled - role is used for subjects admitted to the experimental portion of the design.
   */
  ResearchSubject: v3_RoleClass_ResearchSubject,
  /**
   * Material (player) sold by a retailer (scoper), who also give advice to prospective buyers.
   */
  RetailedMaterial: v3_RoleClass_RetailedMaterial,
  /**
   * A product regulated by some governmentatl orgnization.  The role is played by Material and scoped by Organization.
   *
   *                         Rationale: To support an entity clone used to identify the NDC number for a drug product.
   */
  RegulatedProduct: v3_RoleClass_RegulatedProduct,
  /**
   * Corresponds to the Role class
   */
  Role: v3_RoleClass_Role,
  /**
   * The "same" role asserts an identity between playing and scoping entities, i.e., that they are in fact two records of the same entity instance, and, in the case of discrepancies (e.g different DOB, gender), that one or both are in error.
   *
   *
   *                            Usage:
   *
   *
   *                         playing and scoping entities must have same classCode, but need not have identical attributes or values.
   *
   *
   *                            Example:
   *
   *
   *                         a provider registry maintains sets of conflicting demographic data for what is reported to be the same individual.
   */
  Same: v3_RoleClass_Same,
  /**
   * A role played by a place at which services may be provided.
   */
  ServiceDeliveryLocation: v3_RoleClass_ServiceDeliveryLocation,
  /**
   * The "same" roleclass asserts an identity between playing and scoping entities: that they are in fact instances of the same entity and, in the case of discrepancies (e.g different DOB, gender), that one or both are in error.
   *
   *
   *                            Usage:
   *
   *
   *                         playing and scoping entities must have same classcode, but need not have identical attributes or values.
   *
   *
   *                            Example:
   *
   *
   *                         a provider registry maintains sets of conflicting demographic data for what is reported to be the same individual.
   */
  Self: v3_RoleClass_Self,
  /**
   * The role of a person (player) who is the officer or signature authority for of a scoping entity, usually an organization (scoper).
   */
  SigningAuthorityOrOfficer: v3_RoleClass_SigningAuthorityOrOfficer,
  /**
   * A role played by a material entity that is a specimen for an act. It is scoped by the source of the specimen.
   */
  Specimen: v3_RoleClass_Specimen,
  /**
   * A role played by an entity, usually an organization that is the sponsor of an insurance plan or a health program. A sponsor is the party that is ultimately accountable for the coverage by employment contract or by law.  A sponsor can be an employer, union, government agency, or association.  Fully insured sponsors establish the terms of the plan and contract with health insurance plans to assume the risk and to administer the plan.  Self-insured sponsors delegate coverage administration, but not risk, to third-party administrators.  Program sponsors designate services to be covered in accordance with statute.   Program sponsors may administer the coverage themselves, delegate coverage administration, but not risk to third-party administrators, or contract with health insurance plans to assume the risk and administrator a program. Sponsors qualify individuals who may become
   *
   *
   *
   *                               a policy holder of the plan;
   *
   *
   *
   *                               where the sponsor is the policy holder, who may become a subscriber or a dependent to a policy under the plan; or
   *
   *
   *
   *                               where the sponsor is a government agency, who may become program eligibles under a program.
   *
   *
   *
   *                         The sponsor role may be further qualified by the SponsorRole.code.  Entities playing the sponsor role may also play the role of a Coverage Administrator.
   *
   *
   *                            Example: An employer, union, government agency, or association.
   */
  CoverageSponsor: v3_RoleClass_CoverageSponsor,
  /**
   * A stabilizer (player) added to a mixture (scoper) in order to prevent the molecular disintegration of the main substance.
   */
  Stabilizer: v3_RoleClass_Stabilizer,
  /**
   * A role played by an individual who is a student of a school, which is the scoping entity.
   */
  Student: v3_RoleClass_Student,
  /**
   * Relates an entity (player) (e.g. a device) to a location (scoper) at which it is normally found or stored when not used.
   */
  StoredEntity: v3_RoleClass_StoredEntity,
  /**
   * An entity that subsumes the identity of another.  Used in the context of merging documented entity instances. Both the player and scoper must have the same classCode.
   *
   *                         The use of this code is deprecated in favor of the term SUBY which is its inverse and is more ontologically correct.
   */
  Subsumer: v3_RoleClass_Subsumer,
  /**
   * Description: A role played by a person covered under a policy based on association with a sponsor who is the policy holder, and whose association may provide for the eligibility of dependents for coverage.
   *
   *
   *                            Discussion: The policy holder holds the contract with the policy or program underwriter.  The subscriber holds a certificate of coverage under the contract.  In legal proceedings concerning the policy or program, the terms of the contract takes precedence over the terms of the certificate of coverage if there are any inconsistencies.
   *
   *
   *                            Note: The party playing the role of a subscriber is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a subscriber may make a claim under a policy, e.g., a subscriber under a health insurance policy may become the claimant for coverage under that policy for wellness examines or if injured and there is no liable third party.  In the case of a subscriber making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that the subscriber has filed a claim for services covered under the health insurance policy.
   *
   *
   *                            Example: An employee or a member of an association.
   */
  Subscriber: v3_RoleClass_Subscriber,
  /**
   * Relates a prevailing record of an Entity (scoper) with another record (player) that it subsumes.
   *
   *
   *                            Examples: Show a correct new Person object (scoper) that subsumes one or more duplicate Person objects that had accidentally been created for the same physical person.
   *
   *
   *                            Constraints: Both the player and scoper must have the same classCode.
   */
  SubsumedBy: v3_RoleClass_SubsumedBy,
  /**
   * Relates a place entity (player) as the region over which the scoper (typically an Organization) has certain authority (jurisdiction). For example, the Calgary Regional Health Authority (scoper) has authority over the territory "Region 4 of Alberta" (player) in matters of health.
   */
  TerritoryOfAuthority: v3_RoleClass_TerritoryOfAuthority,
  /**
   * A manufactured material (player) that is used for its therapeutic properties.  The manufacturer is the scoper.
   */
  TherapeuticAgent: v3_RoleClass_TherapeuticAgent,
  /**
   * A role played by a person or an organization.  It is the party that
   *
   *
   *
   *                               accepts fiscal responsibility for insurance plans and the policies created under those plans;
   *
   *
   *
   *                               administers and accepts fiscal responsibility for a program that provides coverage for services to eligible individuals; and/or
   *
   *
   *
   *                               has the responsibility to assess the merits of each risk and decide a suitable premium for accepting all or part of the risk.  If played by an organization, this role may be further specified by an appropriate RoleCode.
   *
   *
   *
   *
   *                            Example:
   *
   *
   *
   *
   *                               A health insurer;
   *
   *
   *
   *                               Medicaid Program;
   *
   *
   *
   *                               Lloyd's of London
   */
  Underwriter: v3_RoleClass_Underwriter,
  /**
   * Description:An entity (player) that is used by another entity (scoper)
   */
  UsedEntity: v3_RoleClass_UsedEntity,
  /**
   * A role a product plays when a guarantee is given to the purchaser by the seller (scoping entity) stating that the product is reliable and free from known defects and that the seller will repair or replace defective parts within a given time limit and under certain conditions.
   */
  WarrantedProduct: v3_RoleClass_WarrantedProduct,
};
/**
 *  An entity (player) that acts or is authorized to act on behalf of another entity (scoper).
 */
export const V3RoleClassAgent = {
  /**
   * An entity (player) that acts or is authorized to act on behalf of another entity (scoper).
   */
  Agent: v3_RoleClass_Agent,
  /**
   * An agent role in which the agent is an Entity acting in the employ of an organization.  The focus is on functional role on behalf of the organization, unlike the Employee role where the focus is on the 'Human Resources' relationship between the employee and the organization.
   */
  AssignedEntity: v3_RoleClass_AssignedEntity,
  /**
   * An Entity that is authorized to issue or instantiate permissions, privileges, credentials or other formal/legal authorizations.
   */
  CommissioningParty: v3_RoleClass_CommissioningParty,
  /**
   * A person or an organization (player) which provides or receives information regarding another entity (scoper).  Examples; patient NOK and emergency contacts; guarantor contact; employer contact.
   */
  Contact: v3_RoleClass_Contact,
  /**
   * An entity to be contacted in the event of an emergency.
   */
  EmergencyContact: v3_RoleClass_EmergencyContact,
  /**
   * Guardian of a ward
   */
  Guardian: v3_RoleClass_Guardian,
  /**
   * An individual designated for notification as the next of kin for a given entity.
   */
  NextOfKin: v3_RoleClass_NextOfKin,
  /**
   * The role of a person (player) who is the officer or signature authority for of a scoping entity, usually an organization (scoper).
   */
  SigningAuthorityOrOfficer: v3_RoleClass_SigningAuthorityOrOfficer,
};
/**
 *  A general association between two entities that is neither partitive nor ontological.
 */
export const V3RoleClassAssociative = {
  /**
   * A relationship that is based on mutual behavior of the two Entities as being related. The basis of such relationship may be agreements (e.g., spouses, contract parties) or they may be de facto behavior (e.g. friends) or may be an incidental involvement with each other (e.g. parties over a dispute, siblings, children).
   */
  RoleClassMutualRelationship: v3_RoleClass_RoleClassMutualRelationship,
  /**
   * An association for a playing Entity that is used, known, treated, handled, built, or destroyed, etc. under the auspices of the scoping Entity. The playing Entity is passive in these roles (even though it may be active in other roles), in the sense that the kinds of things done to it in this role happen without an agreement from the playing Entity.
   */
  RoleClassPassive: v3_RoleClass_RoleClassPassive,
  /**
   * A relationship between two entities that is formally recognized, frequently by a contract or similar agreement.
   */
  RoleClassRelationshipFormal: v3_RoleClass_RoleClassRelationshipFormal,
  /**
   * A role in which the playing entity (material) provides access to another entity. The principal use case is intravenous (or other bodily) access lines that preexist and need to be referred to for medication routing instructions.
   */
  Access: v3_RoleClass_Access,
  /**
   * A physical association whereby two Entities are in some (even lose) spatial relationship with each other such that they touch each other in some way.
   *
   *
   *                            Examples: the colon is connected (and therefore adjacent) to the jejunum; the colon is adjacent to the liver (even if not actually connected.)
   *
   *
   *                            UsageConstraints: Adjacency is in principle a symmetrical connection, but scoper and player of the role should, where applicable, be assigned to have scoper be the larger, more central Entity and player the smaller, more distant, appendage.
   */
  Adjacency: v3_RoleClass_Adjacency,
  /**
   * A material (player) that can be administered to an Entity (scoper).
   */
  AdministerableMaterial: v3_RoleClass_AdministerableMaterial,
  /**
   * Player of the Affiliate role has a business/professional relationship with scoper.  Player and scoper may be persons or organization.  The Affiliate relationship does not imply membership in a group, nor does it exist for resource scheduling purposes.
   *
   *
   *                            Example: A healthcare provider is affiliated with another provider as a business associate.
   */
  Affiliate: v3_RoleClass_Affiliate,
  /**
   * An entity (player) that acts or is authorized to act on behalf of another entity (scoper).
   */
  Agent: v3_RoleClass_Agent,
  /**
   * An agent role in which the agent is an Entity acting in the employ of an organization.  The focus is on functional role on behalf of the organization, unlike the Employee role where the focus is on the 'Human Resources' relationship between the employee and the organization.
   */
  AssignedEntity: v3_RoleClass_AssignedEntity,
  /**
   * Relates a place (playing Entity) as the location where a living subject (scoping Entity) was born.
   */
  Birthplace: v3_RoleClass_Birthplace,
  /**
   * A connection between two atoms of a molecule.
   *
   *
   *                            Examples: double bond between first and second C in ethane, peptide bond between two amino-acid, disulfide bridge between two proteins, chelate and ion associations, even the much weaker van-der-Waals bonds can be considered molecular bonds.
   *
   *
   *                            UsageConstraints: See connection and adjacency for the assignment of player and scoper.
   */
  MolecularBond: v3_RoleClass_MolecularBond,
  /**
   * A person responsible for the primary care of a patient at home.
   */
  Caregiver: v3_RoleClass_Caregiver,
  /**
   * A person, non-person living subject, or place that is the subject of an investigation related to a notifiable condition (health circumstance that is reportable within the applicable public health jurisdiction)
   */
  CaseSubject: v3_RoleClass_CaseSubject,
  /**
   * Citizen of apolitical entity
   */
  Citizen: v3_RoleClass_Citizen,
  /**
   * Description: A role played by a party making a claim for coverage under a policy or program.  A claimant must be either a person or organization, or a group of persons or organizations.  A claimant is not a named insured or a program eligible.
   *
   *
   *                            Discussion: With respect to liability insurance such as property and casualty insurance, a claimant must file a claim requesting indemnification for a loss that the claimant considers covered under the policy of a named insured.  The claims adjuster for the policy underwriter will review the claim to determine whether the loss meets the benefit coverage criteria under a policy, and base any indemnification or coverage payment on that review.  If a third party is liable in whole or part for the loss, the underwriter may pursue third party liability recovery.  A claimant may be involved in civil or criminal legal proceedings involving claims against a defendant party that is indemnified by an insurance policy or to protest the finding of a claims adjustor. With respect to life insurance, a beneficiary designated by a named insured becomes a claimant of the proceeds of coverage, as in the case of a life insurance policy.  However, a claimant for coverage under life insurance is not necessarily a designated beneficiary.
   *
   *
   *                            Note: A claimant is not a named insured.  However, a named insured may make a claim under a policy, e.g., an insured driver may make a claim for an injury under his or her comprehensive automobile insurance policy.  Similarly, a program eligible may make a claim under program, e.g., an unemployed worker may claim benefits under an unemployment insurance program, but parties playing these covered party role classes are not, for purposes of this vocabulary and in an effort to clearly distinguish role classes, considered claimants.
   *
   *                         In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that either a named insured or an individual insured has filed a claim for a loss.  In the case of a program eligible, a role type code INJWKR (injured worker) subtypes the class to indicate that the covered party in a workers compensation program is an injured worker, and as such, has filed a "claim" under the program for benefits.  Likewise, a covered role type code UNEMP (unemployed worker) subtypes the program eligible class to indicate that the covered party in an unemployment insurance program has filed a claim for unemployment benefits.
   *
   *
   *                            Example: A claimant under automobile policy that is not the named insured.
   */
  Claimant: v3_RoleClass_Claimant,
  /**
   * An Entity that is authorized to issue or instantiate permissions, privileges, credentials or other formal/legal authorizations.
   */
  CommissioningParty: v3_RoleClass_CommissioningParty,
  /**
   * A person or an organization (player) which provides or receives information regarding another entity (scoper).  Examples; patient NOK and emergency contacts; guarantor contact; employer contact.
   */
  Contact: v3_RoleClass_Contact,
  /**
   * An adjacency of two Entities held together by a bond which attaches to each of the two entities.
   *
   *
   *                            Examples: biceps brachii muscle connected to the radius bone, port 3 on a network switch connected to port 5 on a patch panel.
   *
   *
   *                            UsageConstraints: See Adjacency for the assignment of scoper (larger, more central) and player (smaller, more distant).
   */
  Connection: v3_RoleClass_Connection,
  /**
   * A connection between two regional parts.
   *
   *
   *                            Examples:  the connection between ascending aorta and the aortic arc, connection between descending colon and sigmoid.
   *
   *
   *                            UsageConstraints: See connection and adjacency for the assignment of player and scoper.
   */
  Continuity: v3_RoleClass_Continuity,
  /**
   * A role class played by a person who receives benefit coverage under the terms of a particular insurance policy.  The underwriter of that policy is the scoping entity.  The covered party receives coverage because of some contractual or other relationship with the holder of that policy.
   *
   *
   *                            Discussion:This reason for coverage is captured in 'Role.code' and a relationship link with type code of indirect authority should be included using the policy holder role as the source, and the covered party role as the target.
   *
   *                         Note that a particular policy may cover several individuals one of whom may be, but need not be, the policy holder.  Thus the notion of covered party is a role that is distinct from that of the policy holder.
   */
  CoveredParty: v3_RoleClass_CoveredParty,
  /**
   * A role played by a provider, always a person, who has agency authority from a Clinical Research Sponsor to direct the conduct of a clinical research trial or study on behalf of the sponsor.
   */
  ClinicalResearchInvestigator: v3_RoleClass_ClinicalResearchInvestigator,
  /**
   * A role played by an entity, usually an organization, that is the sponsor of a clinical research trial or study.  The sponsor commissions the study, bears the expenses, is responsible for satisfying all legal requirements concerning subject safety and privacy, and is generally responsible for collection, storage and analysis of the data generated during the trial.  No scoper is necessary, as a clinical research sponsor undertakes the role on its own authority and declaration. Clinical research sponsors are usually educational or other research organizations, government agencies or biopharmaceutical companies.
   */
  ClinicalResearchSponsor: v3_RoleClass_ClinicalResearchSponsor,
  /**
   * Definition: Relates a place (playing Entity) as the location where a living subject (scoping Entity) died.
   */
  PlaceOfDeath: v3_RoleClass_PlaceOfDeath,
  /**
   * Description: A role played by a person covered under a policy or program based on an association with a subscriber, which is recognized by the policy holder.
   *
   *
   *                            Note:  The party playing the role of a dependent is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a dependent may make a claim under a policy, e.g., a dependent under a health insurance policy may become the claimant for coverage under that policy for wellness examines or if injured and there is no liable third party.  In the case of a dependent making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that the dependent has filed a claim for services covered under the health insurance policy.
   *
   *
   *                            Example: The dependent has an association with the subscriber such as a financial dependency or personal relationship such as that of a spouse, or a natural or adopted child.  The policy holder may be required by law to recognize certain associations or may have discretion about the associations.  For example, a policy holder may dictate the criteria for the dependent status of adult children who are students, such as requiring full time enrollment, or may recognize domestic partners as dependents.  Under certain circumstances, the dependent may be under the indirect authority of a responsible party acting as a surrogate for the subscriber, for example, if the subscriber is differently abled or deceased, a guardian ad Lidem or estate executor may be appointed to assume the subscriberaTMs legal standing in the relationship with the dependent.
   */
  Dependent: v3_RoleClass_Dependent,
  /**
   * A role of a place (player) that is intended to house the provision of services. Scoper is the Entity (typically Organization) that provides these services. This is not synonymous with "ownership."
   */
  DedicatedServiceDeliveryLocation: v3_RoleClass_DedicatedServiceDeliveryLocation,
  /**
   * A material (player) distributed by a distributor (scoper) who functions between a manufacturer and a buyer or retailer.
   */
  DistributedMaterial: v3_RoleClass_DistributedMaterial,
  /**
   * An entity to be contacted in the event of an emergency.
   */
  EmergencyContact: v3_RoleClass_EmergencyContact,
  /**
   * A relationship between a person or organization and a person or organization formed for the purpose of exchanging work for compensation.  The purpose of the role is to identify the type of relationship the employee has to the employer, rather than the nature of the work actually performed.  (Contrast with AssignedEntity.)
   */
  Employee: v3_RoleClass_Employee,
  /**
   * A role played by a place at which the location of an event may be recorded.
   */
  EventLocation: v3_RoleClass_EventLocation,
  /**
   * A role played by an entity that has been exposed to a person or animal suffering a contagious disease, or with a location from which a toxin has been distributed.  The player of the role is normally a person or animal, but it is possible that other entity types could become exposed.  The role is scoped by the source of the exposure, and it is quite possible for a person playing the role of exposed party to also become the scoper a role played by another person.  That is to say, once a person has become infected, it is possible, perhaps likely, for that person to infect others.
   *
   *                         Management of exposures and tracking exposed parties is a key function within public health, and within most public health contexts - exposed parties are known as "contacts."
   */
  ExposedEntity: v3_RoleClass_ExposedEntity,
  /**
   * A person or organization (player) that serves as a financial guarantor for another person or organization (scoper).
   */
  Guarantor: v3_RoleClass_Guarantor,
  /**
   * Guardian of a ward
   */
  Guardian: v3_RoleClass_Guardian,
  /**
   * Entity that is currently in the possession of a holder (scoper), who holds, or uses it, usually based on some agreement with the owner.
   */
  HeldEntity: v3_RoleClass_HeldEntity,
  /**
   * The role of a material (player) that is the physical health chart belonging to an organization (scoper).
   */
  HealthChart: v3_RoleClass_HealthChart,
  /**
   * A role in which the scoping entity designates an identifier for a playing entity.
   */
  IdentifiedEntity: v3_RoleClass_IdentifiedEntity,
  /**
   * Description: A role played by a party covered under a policy as the policy holder.  An individual may be either a person or an organization.
   *
   *
   *                            Note: The party playing the role of an individual insured is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a named insured may make a claim under a policy, e.g., a party that is the named insured and policy holder under a comprehensive automobile insurance policy may become the claimant for coverage under that policy if injured in an automobile accident and there is no liable third party.  In the case of an individual insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that an individual insured has filed a claim for a loss.
   *
   *
   *                            Example: The individual insured under a comprehensive automobile, disability, or property and casualty policy that is the policy holder.
   */
  Individual: v3_RoleClass_Individual,
  /**
   * An entity that is the subject of an investigation. This role is scoped by the party responsible for the investigation.
   */
  InvestigationSubject: v3_RoleClass_InvestigationSubject,
  /**
   * A role played by a place at which health care services may be provided without prior designation or authorization.
   */
  IncidentalServiceDeliveryLocation: v3_RoleClass_IncidentalServiceDeliveryLocation,
  /**
   * A relationship in which the scoper certifies the player ( e. g. a medical care giver, a medical device or a provider organization) to perform certain activities that fall under the jurisdiction of the scoper (e.g., a health authority licensing healthcare providers, a medical quality authority certifying healthcare professionals).
   */
  LicensedEntity: v3_RoleClass_LicensedEntity,
  /**
   * Scoped by the manufacturer
   */
  ManufacturedProduct: v3_RoleClass_ManufacturedProduct,
  /**
   * A role played by a member of a military service. Scoper is the military service (e.g. Army, Navy, Air Force, etc.) or, more specifically, the unit (e.g. Company C, 3rd Battalion, 4th Division, etc.)
   */
  MilitaryPerson: v3_RoleClass_MilitaryPerson,
  /**
   * An entity (player) that is maintained by another entity (scoper).  This is typical role held by durable equipment. The scoper assumes responsibility for proper operation, quality, and safety.
   */
  MaintainedEntity: v3_RoleClass_MaintainedEntity,
  /**
   * Description: A role played by a party to an insurance policy to which the insurer agrees to indemnify for losses, provides benefits for, or renders services.  A named insured may be either a person, non-person living subject, or an organization, or a group of persons, non-person living subjects, or organizations.
   *
   *
   *                            Discussion: The coded concept NAMED should not be used where a more specific child concept in this Specializable value set applies.  In some cases, the named insured may not be the policy holder, e.g., where a policy holder purchases life insurance policy in which another party is the named insured and the policy holder is the beneficiary of the policy.
   *
   *
   *                            Note: The party playing the role of a named insured is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a named insured may make a claim under a policy, e.g., e.g., a party that is the named insured and policy holder under a comprehensive automobile insurance policy may become the claimant for coverage under that policy e.g., if injured in an automobile accident and there is no liable third party.  In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that a named insured has filed a claim for a loss.
   *
   *
   *                            Example: The named insured under a comprehensive automobile, disability, or property and casualty policy that is the named insured and may or may not be the policy holder.
   */
  NamedInsured: v3_RoleClass_NamedInsured,
  /**
   * An individual designated for notification as the next of kin for a given entity.
   */
  NextOfKin: v3_RoleClass_NextOfKin,
  /**
   * notary public
   */
  NotaryPublic: v3_RoleClass_NotaryPublic,
  /**
   * An Entity (player) for which someone (scoper) is granted by law the right to call the material (player) his own.  This entitles the scoper to make decisions about the disposition of that material.
   */
  OwnedEntity: v3_RoleClass_OwnedEntity,
  /**
   * A Role of a LivingSubject (player) as an actual or potential recipient of health care services from a healthcare provider organization (scoper).
   *
   *
   *                            Usage Note: Communication about relationships between patients and specific healthcare practitioners (people) is not done via scoper.  Instead this is generally done using the CareProvision act.  This allows linkage between patient and a particular healthcare practitioner role and also allows description of the type of care involved in the relationship.
   */
  Patient: v3_RoleClass_Patient,
  /**
   * The role of an organization or individual designated to receive payment for a claim against a particular coverage. The scoping entity is the organization that is the submitter of the invoice in question.
   */
  Payee: v3_RoleClass_Payee,
  /**
   * The role of an organization that undertakes to accept claims invoices, assess the coverage or payments due for those invoices and pay to the designated payees for those invoices.  This role may be either the underwriter or a third-party organization authorized by the underwriter.  The scoping entity is the organization that underwrites the claimed coverage.
   */
  InvoicePayor: v3_RoleClass_InvoicePayor,
  /**
   * A role played by a person or organization that holds an insurance policy.  The underwriter of that policy is the scoping entity.
   *
   *
   *                            Discussion:The identifier of the policy is captured in 'Role.id' when the Role is a policy holder.
   *
   *                         A particular policy may cover several individuals one of whom may be, but need not be, the policy holder.  Thus the notion of covered party is a role that is distinct from that of the policy holder.
   */
  PolicyHolder: v3_RoleClass_PolicyHolder,
  /**
   * Description: A role played by a party that meets the eligibility criteria for coverage under a program.  A program eligible may be either a person, non-person living subject, or an organization, or a group of persons, non-person living subjects, or organizations.
   *
   *
   *                            Discussion: A program as typically government administered coverage for parties determined eligible under the terms of the program.
   *
   *
   *                            Note: The party playing a program eligible is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However a program eligible may make a claim under program, e.g., an unemployed worker may claim benefits under an unemployment insurance program, but parties playing these covered party role classes are not, for purposes of this vocabulary and in an effort to clearly distinguish role classes, considered claimants.
   *
   *                         In the case of a program eligible, a role type code INJWKR (injured worker) subtypes the class to indicate that the covered party in a workers compensation program is an injured worker, and as such, has filed a "claim" under the program for benefits.  Likewise, a covered role type code UNEMP (unemployed worker) subtypes the program eligible class to indicate that the covered party in an unemployment insurance program has filed a claim for unemployment benefits.
   *
   *
   *                            Example: A party meeting eligibility criteria related to health or financial status, e.g., in the U.S., persons meeting health, demographic, or financial criteria established by state and federal law are eligible for Medicaid.
   */
  ProgramEligible: v3_RoleClass_ProgramEligible,
  /**
   * An Entity (player) that is authorized to provide health care services by some authorizing agency (scoper).
   */
  HealthcareProvider: v3_RoleClass_HealthcareProvider,
  /**
   * Links two entities with classCode PSN (person) in a personal relationship. The character of the relationship must be defined by a PersonalRelationshipRoleType code. The player and scoper are determined by PersonalRelationshipRoleType code as well.
   */
  PersonalRelationship: v3_RoleClass_PersonalRelationship,
  /**
   * An entity (player) that has been recognized as having certain training/experience or other characteristics that would make said entity an appropriate performer for a certain activity. The scoper is an organization that educates or qualifies entities.
   */
  QualifiedEntity: v3_RoleClass_QualifiedEntity,
  /**
   * Definition:Specifies the administrative functionality within a formal experimental design for which the ResearchSubject role was established.
   *
   *
   *                            Examples: Screening - role is used for pre-enrollment evaluation portion of the design; enrolled - role is used for subjects admitted to the experimental portion of the design.
   */
  ResearchSubject: v3_RoleClass_ResearchSubject,
  /**
   * Material (player) sold by a retailer (scoper), who also give advice to prospective buyers.
   */
  RetailedMaterial: v3_RoleClass_RetailedMaterial,
  /**
   * A product regulated by some governmentatl orgnization.  The role is played by Material and scoped by Organization.
   *
   *                         Rationale: To support an entity clone used to identify the NDC number for a drug product.
   */
  RegulatedProduct: v3_RoleClass_RegulatedProduct,
  /**
   * A role played by a place at which services may be provided.
   */
  ServiceDeliveryLocation: v3_RoleClass_ServiceDeliveryLocation,
  /**
   * The "same" roleclass asserts an identity between playing and scoping entities: that they are in fact instances of the same entity and, in the case of discrepancies (e.g different DOB, gender), that one or both are in error.
   *
   *
   *                            Usage:
   *
   *
   *                         playing and scoping entities must have same classcode, but need not have identical attributes or values.
   *
   *
   *                            Example:
   *
   *
   *                         a provider registry maintains sets of conflicting demographic data for what is reported to be the same individual.
   */
  Self: v3_RoleClass_Self,
  /**
   * The role of a person (player) who is the officer or signature authority for of a scoping entity, usually an organization (scoper).
   */
  SigningAuthorityOrOfficer: v3_RoleClass_SigningAuthorityOrOfficer,
  /**
   * A role played by an entity, usually an organization that is the sponsor of an insurance plan or a health program. A sponsor is the party that is ultimately accountable for the coverage by employment contract or by law.  A sponsor can be an employer, union, government agency, or association.  Fully insured sponsors establish the terms of the plan and contract with health insurance plans to assume the risk and to administer the plan.  Self-insured sponsors delegate coverage administration, but not risk, to third-party administrators.  Program sponsors designate services to be covered in accordance with statute.   Program sponsors may administer the coverage themselves, delegate coverage administration, but not risk to third-party administrators, or contract with health insurance plans to assume the risk and administrator a program. Sponsors qualify individuals who may become
   *
   *
   *
   *                               a policy holder of the plan;
   *
   *
   *
   *                               where the sponsor is the policy holder, who may become a subscriber or a dependent to a policy under the plan; or
   *
   *
   *
   *                               where the sponsor is a government agency, who may become program eligibles under a program.
   *
   *
   *
   *                         The sponsor role may be further qualified by the SponsorRole.code.  Entities playing the sponsor role may also play the role of a Coverage Administrator.
   *
   *
   *                            Example: An employer, union, government agency, or association.
   */
  CoverageSponsor: v3_RoleClass_CoverageSponsor,
  /**
   * A role played by an individual who is a student of a school, which is the scoping entity.
   */
  Student: v3_RoleClass_Student,
  /**
   * Description: A role played by a person covered under a policy based on association with a sponsor who is the policy holder, and whose association may provide for the eligibility of dependents for coverage.
   *
   *
   *                            Discussion: The policy holder holds the contract with the policy or program underwriter.  The subscriber holds a certificate of coverage under the contract.  In legal proceedings concerning the policy or program, the terms of the contract takes precedence over the terms of the certificate of coverage if there are any inconsistencies.
   *
   *
   *                            Note: The party playing the role of a subscriber is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a subscriber may make a claim under a policy, e.g., a subscriber under a health insurance policy may become the claimant for coverage under that policy for wellness examines or if injured and there is no liable third party.  In the case of a subscriber making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that the subscriber has filed a claim for services covered under the health insurance policy.
   *
   *
   *                            Example: An employee or a member of an association.
   */
  Subscriber: v3_RoleClass_Subscriber,
  /**
   * Relates a place entity (player) as the region over which the scoper (typically an Organization) has certain authority (jurisdiction). For example, the Calgary Regional Health Authority (scoper) has authority over the territory "Region 4 of Alberta" (player) in matters of health.
   */
  TerritoryOfAuthority: v3_RoleClass_TerritoryOfAuthority,
  /**
   * A manufactured material (player) that is used for its therapeutic properties.  The manufacturer is the scoper.
   */
  TherapeuticAgent: v3_RoleClass_TherapeuticAgent,
  /**
   * A role played by a person or an organization.  It is the party that
   *
   *
   *
   *                               accepts fiscal responsibility for insurance plans and the policies created under those plans;
   *
   *
   *
   *                               administers and accepts fiscal responsibility for a program that provides coverage for services to eligible individuals; and/or
   *
   *
   *
   *                               has the responsibility to assess the merits of each risk and decide a suitable premium for accepting all or part of the risk.  If played by an organization, this role may be further specified by an appropriate RoleCode.
   *
   *
   *
   *
   *                            Example:
   *
   *
   *
   *
   *                               A health insurer;
   *
   *
   *
   *                               Medicaid Program;
   *
   *
   *
   *                               Lloyd's of London
   */
  Underwriter: v3_RoleClass_Underwriter,
  /**
   * Description:An entity (player) that is used by another entity (scoper)
   */
  UsedEntity: v3_RoleClass_UsedEntity,
  /**
   * A role a product plays when a guarantee is given to the purchaser by the seller (scoping entity) stating that the product is reliable and free from known defects and that the seller will repair or replace defective parts within a given time limit and under certain conditions.
   */
  WarrantedProduct: v3_RoleClass_WarrantedProduct,
};
/**
 *  Scoped by the manufacturer
 */
export const V3RoleClassManufacturedProduct = {
  /**
   * Scoped by the manufacturer
   */
  ManufacturedProduct: v3_RoleClass_ManufacturedProduct,
  /**
   * A manufactured material (player) that is used for its therapeutic properties.  The manufacturer is the scoper.
   */
  TherapeuticAgent: v3_RoleClass_TherapeuticAgent,
};
/**
 *  A relationship that is based on mutual behavior of the two Entities as being related. The basis of such relationship may be agreements (e.g., spouses, contract parties) or they may bede facto
 * behavior (e.g. friends) or may be an incidental involvement with each other (e.g. parties over a dispute, siblings, children).
 */
export const V3RoleClassMutualRelationship = {
  /**
   * A relationship between two entities that is formally recognized, frequently by a contract or similar agreement.
   */
  RoleClassRelationshipFormal: v3_RoleClass_RoleClassRelationshipFormal,
  /**
   * Player of the Affiliate role has a business/professional relationship with scoper.  Player and scoper may be persons or organization.  The Affiliate relationship does not imply membership in a group, nor does it exist for resource scheduling purposes.
   *
   *
   *                            Example: A healthcare provider is affiliated with another provider as a business associate.
   */
  Affiliate: v3_RoleClass_Affiliate,
  /**
   * An entity (player) that acts or is authorized to act on behalf of another entity (scoper).
   */
  Agent: v3_RoleClass_Agent,
  /**
   * An agent role in which the agent is an Entity acting in the employ of an organization.  The focus is on functional role on behalf of the organization, unlike the Employee role where the focus is on the 'Human Resources' relationship between the employee and the organization.
   */
  AssignedEntity: v3_RoleClass_AssignedEntity,
  /**
   * A person responsible for the primary care of a patient at home.
   */
  Caregiver: v3_RoleClass_Caregiver,
  /**
   * A person, non-person living subject, or place that is the subject of an investigation related to a notifiable condition (health circumstance that is reportable within the applicable public health jurisdiction)
   */
  CaseSubject: v3_RoleClass_CaseSubject,
  /**
   * Citizen of apolitical entity
   */
  Citizen: v3_RoleClass_Citizen,
  /**
   * Description: A role played by a party making a claim for coverage under a policy or program.  A claimant must be either a person or organization, or a group of persons or organizations.  A claimant is not a named insured or a program eligible.
   *
   *
   *                            Discussion: With respect to liability insurance such as property and casualty insurance, a claimant must file a claim requesting indemnification for a loss that the claimant considers covered under the policy of a named insured.  The claims adjuster for the policy underwriter will review the claim to determine whether the loss meets the benefit coverage criteria under a policy, and base any indemnification or coverage payment on that review.  If a third party is liable in whole or part for the loss, the underwriter may pursue third party liability recovery.  A claimant may be involved in civil or criminal legal proceedings involving claims against a defendant party that is indemnified by an insurance policy or to protest the finding of a claims adjustor. With respect to life insurance, a beneficiary designated by a named insured becomes a claimant of the proceeds of coverage, as in the case of a life insurance policy.  However, a claimant for coverage under life insurance is not necessarily a designated beneficiary.
   *
   *
   *                            Note: A claimant is not a named insured.  However, a named insured may make a claim under a policy, e.g., an insured driver may make a claim for an injury under his or her comprehensive automobile insurance policy.  Similarly, a program eligible may make a claim under program, e.g., an unemployed worker may claim benefits under an unemployment insurance program, but parties playing these covered party role classes are not, for purposes of this vocabulary and in an effort to clearly distinguish role classes, considered claimants.
   *
   *                         In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that either a named insured or an individual insured has filed a claim for a loss.  In the case of a program eligible, a role type code INJWKR (injured worker) subtypes the class to indicate that the covered party in a workers compensation program is an injured worker, and as such, has filed a "claim" under the program for benefits.  Likewise, a covered role type code UNEMP (unemployed worker) subtypes the program eligible class to indicate that the covered party in an unemployment insurance program has filed a claim for unemployment benefits.
   *
   *
   *                            Example: A claimant under automobile policy that is not the named insured.
   */
  Claimant: v3_RoleClass_Claimant,
  /**
   * An Entity that is authorized to issue or instantiate permissions, privileges, credentials or other formal/legal authorizations.
   */
  CommissioningParty: v3_RoleClass_CommissioningParty,
  /**
   * A person or an organization (player) which provides or receives information regarding another entity (scoper).  Examples; patient NOK and emergency contacts; guarantor contact; employer contact.
   */
  Contact: v3_RoleClass_Contact,
  /**
   * A role class played by a person who receives benefit coverage under the terms of a particular insurance policy.  The underwriter of that policy is the scoping entity.  The covered party receives coverage because of some contractual or other relationship with the holder of that policy.
   *
   *
   *                            Discussion:This reason for coverage is captured in 'Role.code' and a relationship link with type code of indirect authority should be included using the policy holder role as the source, and the covered party role as the target.
   *
   *                         Note that a particular policy may cover several individuals one of whom may be, but need not be, the policy holder.  Thus the notion of covered party is a role that is distinct from that of the policy holder.
   */
  CoveredParty: v3_RoleClass_CoveredParty,
  /**
   * A role played by a provider, always a person, who has agency authority from a Clinical Research Sponsor to direct the conduct of a clinical research trial or study on behalf of the sponsor.
   */
  ClinicalResearchInvestigator: v3_RoleClass_ClinicalResearchInvestigator,
  /**
   * A role played by an entity, usually an organization, that is the sponsor of a clinical research trial or study.  The sponsor commissions the study, bears the expenses, is responsible for satisfying all legal requirements concerning subject safety and privacy, and is generally responsible for collection, storage and analysis of the data generated during the trial.  No scoper is necessary, as a clinical research sponsor undertakes the role on its own authority and declaration. Clinical research sponsors are usually educational or other research organizations, government agencies or biopharmaceutical companies.
   */
  ClinicalResearchSponsor: v3_RoleClass_ClinicalResearchSponsor,
  /**
   * Description: A role played by a person covered under a policy or program based on an association with a subscriber, which is recognized by the policy holder.
   *
   *
   *                            Note:  The party playing the role of a dependent is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a dependent may make a claim under a policy, e.g., a dependent under a health insurance policy may become the claimant for coverage under that policy for wellness examines or if injured and there is no liable third party.  In the case of a dependent making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that the dependent has filed a claim for services covered under the health insurance policy.
   *
   *
   *                            Example: The dependent has an association with the subscriber such as a financial dependency or personal relationship such as that of a spouse, or a natural or adopted child.  The policy holder may be required by law to recognize certain associations or may have discretion about the associations.  For example, a policy holder may dictate the criteria for the dependent status of adult children who are students, such as requiring full time enrollment, or may recognize domestic partners as dependents.  Under certain circumstances, the dependent may be under the indirect authority of a responsible party acting as a surrogate for the subscriber, for example, if the subscriber is differently abled or deceased, a guardian ad Lidem or estate executor may be appointed to assume the subscriberaTMs legal standing in the relationship with the dependent.
   */
  Dependent: v3_RoleClass_Dependent,
  /**
   * An entity to be contacted in the event of an emergency.
   */
  EmergencyContact: v3_RoleClass_EmergencyContact,
  /**
   * A relationship between a person or organization and a person or organization formed for the purpose of exchanging work for compensation.  The purpose of the role is to identify the type of relationship the employee has to the employer, rather than the nature of the work actually performed.  (Contrast with AssignedEntity.)
   */
  Employee: v3_RoleClass_Employee,
  /**
   * A person or organization (player) that serves as a financial guarantor for another person or organization (scoper).
   */
  Guarantor: v3_RoleClass_Guarantor,
  /**
   * Guardian of a ward
   */
  Guardian: v3_RoleClass_Guardian,
  /**
   * Description: A role played by a party covered under a policy as the policy holder.  An individual may be either a person or an organization.
   *
   *
   *                            Note: The party playing the role of an individual insured is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a named insured may make a claim under a policy, e.g., a party that is the named insured and policy holder under a comprehensive automobile insurance policy may become the claimant for coverage under that policy if injured in an automobile accident and there is no liable third party.  In the case of an individual insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that an individual insured has filed a claim for a loss.
   *
   *
   *                            Example: The individual insured under a comprehensive automobile, disability, or property and casualty policy that is the policy holder.
   */
  Individual: v3_RoleClass_Individual,
  /**
   * An entity that is the subject of an investigation. This role is scoped by the party responsible for the investigation.
   */
  InvestigationSubject: v3_RoleClass_InvestigationSubject,
  /**
   * A relationship in which the scoper certifies the player ( e. g. a medical care giver, a medical device or a provider organization) to perform certain activities that fall under the jurisdiction of the scoper (e.g., a health authority licensing healthcare providers, a medical quality authority certifying healthcare professionals).
   */
  LicensedEntity: v3_RoleClass_LicensedEntity,
  /**
   * A role played by a member of a military service. Scoper is the military service (e.g. Army, Navy, Air Force, etc.) or, more specifically, the unit (e.g. Company C, 3rd Battalion, 4th Division, etc.)
   */
  MilitaryPerson: v3_RoleClass_MilitaryPerson,
  /**
   * Description: A role played by a party to an insurance policy to which the insurer agrees to indemnify for losses, provides benefits for, or renders services.  A named insured may be either a person, non-person living subject, or an organization, or a group of persons, non-person living subjects, or organizations.
   *
   *
   *                            Discussion: The coded concept NAMED should not be used where a more specific child concept in this Specializable value set applies.  In some cases, the named insured may not be the policy holder, e.g., where a policy holder purchases life insurance policy in which another party is the named insured and the policy holder is the beneficiary of the policy.
   *
   *
   *                            Note: The party playing the role of a named insured is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a named insured may make a claim under a policy, e.g., e.g., a party that is the named insured and policy holder under a comprehensive automobile insurance policy may become the claimant for coverage under that policy e.g., if injured in an automobile accident and there is no liable third party.  In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that a named insured has filed a claim for a loss.
   *
   *
   *                            Example: The named insured under a comprehensive automobile, disability, or property and casualty policy that is the named insured and may or may not be the policy holder.
   */
  NamedInsured: v3_RoleClass_NamedInsured,
  /**
   * An individual designated for notification as the next of kin for a given entity.
   */
  NextOfKin: v3_RoleClass_NextOfKin,
  /**
   * notary public
   */
  NotaryPublic: v3_RoleClass_NotaryPublic,
  /**
   * A Role of a LivingSubject (player) as an actual or potential recipient of health care services from a healthcare provider organization (scoper).
   *
   *
   *                            Usage Note: Communication about relationships between patients and specific healthcare practitioners (people) is not done via scoper.  Instead this is generally done using the CareProvision act.  This allows linkage between patient and a particular healthcare practitioner role and also allows description of the type of care involved in the relationship.
   */
  Patient: v3_RoleClass_Patient,
  /**
   * The role of an organization or individual designated to receive payment for a claim against a particular coverage. The scoping entity is the organization that is the submitter of the invoice in question.
   */
  Payee: v3_RoleClass_Payee,
  /**
   * The role of an organization that undertakes to accept claims invoices, assess the coverage or payments due for those invoices and pay to the designated payees for those invoices.  This role may be either the underwriter or a third-party organization authorized by the underwriter.  The scoping entity is the organization that underwrites the claimed coverage.
   */
  InvoicePayor: v3_RoleClass_InvoicePayor,
  /**
   * A role played by a person or organization that holds an insurance policy.  The underwriter of that policy is the scoping entity.
   *
   *
   *                            Discussion:The identifier of the policy is captured in 'Role.id' when the Role is a policy holder.
   *
   *                         A particular policy may cover several individuals one of whom may be, but need not be, the policy holder.  Thus the notion of covered party is a role that is distinct from that of the policy holder.
   */
  PolicyHolder: v3_RoleClass_PolicyHolder,
  /**
   * Description: A role played by a party that meets the eligibility criteria for coverage under a program.  A program eligible may be either a person, non-person living subject, or an organization, or a group of persons, non-person living subjects, or organizations.
   *
   *
   *                            Discussion: A program as typically government administered coverage for parties determined eligible under the terms of the program.
   *
   *
   *                            Note: The party playing a program eligible is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However a program eligible may make a claim under program, e.g., an unemployed worker may claim benefits under an unemployment insurance program, but parties playing these covered party role classes are not, for purposes of this vocabulary and in an effort to clearly distinguish role classes, considered claimants.
   *
   *                         In the case of a program eligible, a role type code INJWKR (injured worker) subtypes the class to indicate that the covered party in a workers compensation program is an injured worker, and as such, has filed a "claim" under the program for benefits.  Likewise, a covered role type code UNEMP (unemployed worker) subtypes the program eligible class to indicate that the covered party in an unemployment insurance program has filed a claim for unemployment benefits.
   *
   *
   *                            Example: A party meeting eligibility criteria related to health or financial status, e.g., in the U.S., persons meeting health, demographic, or financial criteria established by state and federal law are eligible for Medicaid.
   */
  ProgramEligible: v3_RoleClass_ProgramEligible,
  /**
   * An Entity (player) that is authorized to provide health care services by some authorizing agency (scoper).
   */
  HealthcareProvider: v3_RoleClass_HealthcareProvider,
  /**
   * Links two entities with classCode PSN (person) in a personal relationship. The character of the relationship must be defined by a PersonalRelationshipRoleType code. The player and scoper are determined by PersonalRelationshipRoleType code as well.
   */
  PersonalRelationship: v3_RoleClass_PersonalRelationship,
  /**
   * An entity (player) that has been recognized as having certain training/experience or other characteristics that would make said entity an appropriate performer for a certain activity. The scoper is an organization that educates or qualifies entities.
   */
  QualifiedEntity: v3_RoleClass_QualifiedEntity,
  /**
   * Definition:Specifies the administrative functionality within a formal experimental design for which the ResearchSubject role was established.
   *
   *
   *                            Examples: Screening - role is used for pre-enrollment evaluation portion of the design; enrolled - role is used for subjects admitted to the experimental portion of the design.
   */
  ResearchSubject: v3_RoleClass_ResearchSubject,
  /**
   * The "same" roleclass asserts an identity between playing and scoping entities: that they are in fact instances of the same entity and, in the case of discrepancies (e.g different DOB, gender), that one or both are in error.
   *
   *
   *                            Usage:
   *
   *
   *                         playing and scoping entities must have same classcode, but need not have identical attributes or values.
   *
   *
   *                            Example:
   *
   *
   *                         a provider registry maintains sets of conflicting demographic data for what is reported to be the same individual.
   */
  Self: v3_RoleClass_Self,
  /**
   * The role of a person (player) who is the officer or signature authority for of a scoping entity, usually an organization (scoper).
   */
  SigningAuthorityOrOfficer: v3_RoleClass_SigningAuthorityOrOfficer,
  /**
   * A role played by an entity, usually an organization that is the sponsor of an insurance plan or a health program. A sponsor is the party that is ultimately accountable for the coverage by employment contract or by law.  A sponsor can be an employer, union, government agency, or association.  Fully insured sponsors establish the terms of the plan and contract with health insurance plans to assume the risk and to administer the plan.  Self-insured sponsors delegate coverage administration, but not risk, to third-party administrators.  Program sponsors designate services to be covered in accordance with statute.   Program sponsors may administer the coverage themselves, delegate coverage administration, but not risk to third-party administrators, or contract with health insurance plans to assume the risk and administrator a program. Sponsors qualify individuals who may become
   *
   *
   *
   *                               a policy holder of the plan;
   *
   *
   *
   *                               where the sponsor is the policy holder, who may become a subscriber or a dependent to a policy under the plan; or
   *
   *
   *
   *                               where the sponsor is a government agency, who may become program eligibles under a program.
   *
   *
   *
   *                         The sponsor role may be further qualified by the SponsorRole.code.  Entities playing the sponsor role may also play the role of a Coverage Administrator.
   *
   *
   *                            Example: An employer, union, government agency, or association.
   */
  CoverageSponsor: v3_RoleClass_CoverageSponsor,
  /**
   * A role played by an individual who is a student of a school, which is the scoping entity.
   */
  Student: v3_RoleClass_Student,
  /**
   * Description: A role played by a person covered under a policy based on association with a sponsor who is the policy holder, and whose association may provide for the eligibility of dependents for coverage.
   *
   *
   *                            Discussion: The policy holder holds the contract with the policy or program underwriter.  The subscriber holds a certificate of coverage under the contract.  In legal proceedings concerning the policy or program, the terms of the contract takes precedence over the terms of the certificate of coverage if there are any inconsistencies.
   *
   *
   *                            Note: The party playing the role of a subscriber is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a subscriber may make a claim under a policy, e.g., a subscriber under a health insurance policy may become the claimant for coverage under that policy for wellness examines or if injured and there is no liable third party.  In the case of a subscriber making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that the subscriber has filed a claim for services covered under the health insurance policy.
   *
   *
   *                            Example: An employee or a member of an association.
   */
  Subscriber: v3_RoleClass_Subscriber,
  /**
   * A role played by a person or an organization.  It is the party that
   *
   *
   *
   *                               accepts fiscal responsibility for insurance plans and the policies created under those plans;
   *
   *
   *
   *                               administers and accepts fiscal responsibility for a program that provides coverage for services to eligible individuals; and/or
   *
   *
   *
   *                               has the responsibility to assess the merits of each risk and decide a suitable premium for accepting all or part of the risk.  If played by an organization, this role may be further specified by an appropriate RoleCode.
   *
   *
   *
   *
   *                            Example:
   *
   *
   *
   *
   *                               A health insurer;
   *
   *
   *
   *                               Medicaid Program;
   *
   *
   *
   *                               Lloyd's of London
   */
  Underwriter: v3_RoleClass_Underwriter,
};
/**
 *  An association between two Entities where the playing Entity is considered in some way "part" of the scoping Entity, e.g., as a member, component, ingredient, or content. Being "part" in the broadest sense of the word can mean anything from being an integral structural component to a mere incidental temporary association of a playing Entity with a (generally larger) scoping Entity.
 */
export const V3RoleClassPartitive = {
  /**
   * Definition: a therapeutically active ingredient (player) in a mixture (scoper), where the mixture is typically a manufactured pharmaceutical.  It is unknown if the quantity of such an ingredient is expressed precisely in terms of the playing ingredient substance, or, if it is specified in terms of a closely related substance (active moiety or reference substance).
   */
  ActiveIngredient: v3_RoleClass_ActiveIngredient,
  /**
   * Description:  Active ingredient, where the ingredient substance (player) is itself the "basis of strength", i.e., where the Role.quantity specifies exactly the quantity of the player substance in the medicine formulation.
   *
   *
   *                            Examples: Lopressor 50 mg actually contains 50 mg of metoprolol succinate, even though the active moiety is metoprolol,  but also: Tenormin 50 mg contain 50 mg of atenolol, as free base, i.e., where the active ingredient atenolol is also the active moiety.
   */
  ActiveIngredientBasisOfStrength: v3_RoleClass_ActiveIngredientBasisOfStrength,
  /**
   * Description: Active ingredient, where not the ingredient substance (player), but itaTMs active moiety is the "basis of strength", i.e., where the Role.quantity specifies the quantity of the player substance's active moiety in the medicine formulation.
   *
   *
   *                            Examples: 1 mL of Betopic 5mg/mL eye drops contains 5.6 mg betaxolol hydrochloride equivalent to betaxolol base 5 mg.
   */
  ActiveIngredientMoietyIsBasisOfStrength: v3_RoleClass_ActiveIngredientMoietyIsBasisOfStrength,
  /**
   * Description: Active ingredient, where not the ingredient substance (player) but another reference substance with the same active moiety, is the "basis of strength", i.e., where the Role.quantity specifies the quantity of a reference substance, similar but different from the player substance's in the medicine formulation.
   *
   *
   *                            Examples: Toprol-XL 50 mg contains 47.5 mg of metoprolol succinate equivalent to 50 mg of metoprolol tartrate.
   */
  ActiveIngredientReferenceSubstanceIsBasisOfStrength: v3_RoleClass_ActiveIngredientReferenceSubstanceIsBasisOfStrength,
  /**
   * The molecule or ion that is responsible for the intended pharmacological action of the drug substance, excluding those appended or associated parts of the molecule that make the molecule an ester, salt (including a salt with hydrogen or coordination bonds), or other noncovalent derivative (such as a complex, chelate, or clathrate).
   *
   *                         Examples: heparin-sodium and heparin-potassium have the same active moiety, heparin; the active moiety of morphine-hydrochloride is morphine.
   */
  ActiveMoiety: v3_RoleClass_ActiveMoiety,
  /**
   * A component (player) added to enhance the action of an active ingredient (scoper) (in the manner of a catalyst) but which has no active effect in and of itself.  Such ingredients are significant in defining equivalence of products in a way that inactive ingredients are not.
   */
  Adjuvant: v3_RoleClass_Adjuvant,
  /**
   * An ingredient (player)  that is added to a base (scoper), that amounts to a minor part of the overall mixture.
   */
  Additive: v3_RoleClass_Additive,
  /**
   * A portion (player) of an original or source specimen (scoper) used for testing or transportation.
   */
  Aliquot: v3_RoleClass_Aliquot,
  /**
   * A base ingredient (player) is what comprises the major part of a mixture (scoper). E.g., Water in most i.v. solutions, or Vaseline in salves. Among all ingredients of a material, there should be only one base. A base substance can, in turn, be a mixture.
   */
  Base: v3_RoleClass_Base,
  /**
   * An ingredient whose presence is not intended but may not be reasonably avoided given the circumstances of the mixture's nature or origin.
   */
  ContaminantIngredient: v3_RoleClass_ContaminantIngredient,
  /**
   * A substance (player) influencing the optical aspect of material (scoper).
   */
  ColorAdditive: v3_RoleClass_ColorAdditive,
  /**
   * Relates a material as the content (player) to a container (scoper).  Unlike ingredients, the content and a container remain separate (not mixed) and the content can be removed from the container.  A content is not part of an empty container.
   */
  Content: v3_RoleClass_Content,
  /**
   * An exposure agent carrier is an entity that is capable of conveying an exposure agent from one entity to another.  The scoper of the role must be the exposure agent (e.g., pathogen).
   */
  ExposureAgentCarrier: v3_RoleClass_ExposureAgentCarrier,
  /**
   * Description: A vector is a living subject that carries an exposure agent.  The vector does not cause the disease itself, but exposes targets to the exposure agent.  A mosquito carrying malaria is an example of a vector.  The scoper of the role must be the exposure agent (e.g., pathogen).
   */
  ExposureVector: v3_RoleClass_ExposureVector,
  /**
   * A substance (player) added to a mixture (scoper) to make it taste a certain way.  In food the use is obvious, in pharmaceuticals flavors can hide disgusting taste of the active ingredient (important in pediatric treatments).
   */
  FlavorAdditive: v3_RoleClass_FlavorAdditive,
  /**
   * Description: A fomite is a non-living entity that is capable of conveying exposure agent from one entity to another.  A doorknob contaminated with a Norovirus is an example of a fomite.  Anyone touching the doorknob would be exposed to the virus.  The scoper of the role must be the exposure agent (e.g., pathogen).
   */
  Fomite: v3_RoleClass_Fomite,
  /**
   * An ingredient which is not considered therapeutically active, e.g., colors, flavors, stabilizers, or preservatives, fillers, or structural components added to an active ingredient in order to facilitate administration of the active ingredient but without being considered therapeutically active. An inactive ingredient need not be biologically inert, e.g., might be active as an allergen or might have a pleasant taste, but is not an essential constituent delivering the therapeutic effect.
   */
  InactiveIngredient: v3_RoleClass_InactiveIngredient,
  /**
   * Relates a component (player) to a mixture (scoper). E.g., Glucose and Water are ingredients of D5W, latex may be an ingredient in a tracheal tube.
   */
  Ingredient: v3_RoleClass_Ingredient,
  /**
   * A microorganism that has been isolated from other microorganisms or a source matrix.
   */
  Isolate: v3_RoleClass_Isolate,
  /**
   * Relates an entity (player) to a location (scoper) at which it is present in some way. This presence may be limited in time.
   */
  LocatedEntity: v3_RoleClass_LocatedEntity,
  /**
   * A role played by an entity that is a member of a group.  The group provides the scope for this role.
   *
   *                         Among other uses, groups as used in insurance (groups of covered individuals) and in scheduling where resources may be grouped for scheduling and logistical purposes.
   */
  Member: v3_RoleClass_Member,
  /**
   * An ingredient (player) of a medication (scoper) that is inseparable from the active ingredients, but has no intended chemical or pharmaceutical effect itself, but which may have some systemic effect on the patient.
   *
   *                         An example is a collagen matrix used as a base for transplanting skin cells.  The collagen matrix can be left permanently in the graft site.  Because it is of bovine origin, the patient may exhibit allergies or may have cultural objections to its use.
   */
  MechanicalIngredient: v3_RoleClass_MechanicalIngredient,
  /**
   * Definition:  an association between two Entities where the playing Entity (the part) is a component of the whole (scoper) in the sense of an integral structural component, that is distinct from other parts in the same whole, has a distinct function in the whole, and, as an effect, the full integrity of the whole depends (to some degree) on the presence of this part, even though the part may often be separable from the whole.
   *
   *
   *                            Discussion: Part is defined in opposition to (a) ingredient (not separable), (b) content (not a functional component), and (c) member (not functionally distinct from other members).
   */
  Part: v3_RoleClass_Part,
  /**
   * A substance (player) added to a mixture (scoper) to prevent microorganisms (fungi, bacteria) to spoil the mixture.
   */
  Preservative: v3_RoleClass_Preservative,
  /**
   * A role played by a material entity that is a specimen for an act. It is scoped by the source of the specimen.
   */
  Specimen: v3_RoleClass_Specimen,
  /**
   * A stabilizer (player) added to a mixture (scoper) in order to prevent the molecular disintegration of the main substance.
   */
  Stabilizer: v3_RoleClass_Stabilizer,
  /**
   * Relates an entity (player) (e.g. a device) to a location (scoper) at which it is normally found or stored when not used.
   */
  StoredEntity: v3_RoleClass_StoredEntity,
};
/**
 *  An association for a playing Entity that is used, known, treated, handled, built, or destroyed, etc. under the auspices of the scoping Entity. The playing Entity is passive in these roles (even though it may be active in other roles), in the sense that the kinds of things done to it in this role happen without an agreement from the playing Entity.
 */
export const V3RoleClassPassive = {
  /**
   * A role in which the playing entity (material) provides access to another entity. The principal use case is intravenous (or other bodily) access lines that preexist and need to be referred to for medication routing instructions.
   */
  Access: v3_RoleClass_Access,
  /**
   * A physical association whereby two Entities are in some (even lose) spatial relationship with each other such that they touch each other in some way.
   *
   *
   *                            Examples: the colon is connected (and therefore adjacent) to the jejunum; the colon is adjacent to the liver (even if not actually connected.)
   *
   *
   *                            UsageConstraints: Adjacency is in principle a symmetrical connection, but scoper and player of the role should, where applicable, be assigned to have scoper be the larger, more central Entity and player the smaller, more distant, appendage.
   */
  Adjacency: v3_RoleClass_Adjacency,
  /**
   * A material (player) that can be administered to an Entity (scoper).
   */
  AdministerableMaterial: v3_RoleClass_AdministerableMaterial,
  /**
   * Relates a place (playing Entity) as the location where a living subject (scoping Entity) was born.
   */
  Birthplace: v3_RoleClass_Birthplace,
  /**
   * A connection between two atoms of a molecule.
   *
   *
   *                            Examples: double bond between first and second C in ethane, peptide bond between two amino-acid, disulfide bridge between two proteins, chelate and ion associations, even the much weaker van-der-Waals bonds can be considered molecular bonds.
   *
   *
   *                            UsageConstraints: See connection and adjacency for the assignment of player and scoper.
   */
  MolecularBond: v3_RoleClass_MolecularBond,
  /**
   * An adjacency of two Entities held together by a bond which attaches to each of the two entities.
   *
   *
   *                            Examples: biceps brachii muscle connected to the radius bone, port 3 on a network switch connected to port 5 on a patch panel.
   *
   *
   *                            UsageConstraints: See Adjacency for the assignment of scoper (larger, more central) and player (smaller, more distant).
   */
  Connection: v3_RoleClass_Connection,
  /**
   * A connection between two regional parts.
   *
   *
   *                            Examples:  the connection between ascending aorta and the aortic arc, connection between descending colon and sigmoid.
   *
   *
   *                            UsageConstraints: See connection and adjacency for the assignment of player and scoper.
   */
  Continuity: v3_RoleClass_Continuity,
  /**
   * Definition: Relates a place (playing Entity) as the location where a living subject (scoping Entity) died.
   */
  PlaceOfDeath: v3_RoleClass_PlaceOfDeath,
  /**
   * A role of a place (player) that is intended to house the provision of services. Scoper is the Entity (typically Organization) that provides these services. This is not synonymous with "ownership."
   */
  DedicatedServiceDeliveryLocation: v3_RoleClass_DedicatedServiceDeliveryLocation,
  /**
   * A material (player) distributed by a distributor (scoper) who functions between a manufacturer and a buyer or retailer.
   */
  DistributedMaterial: v3_RoleClass_DistributedMaterial,
  /**
   * A role played by a place at which the location of an event may be recorded.
   */
  EventLocation: v3_RoleClass_EventLocation,
  /**
   * A role played by an entity that has been exposed to a person or animal suffering a contagious disease, or with a location from which a toxin has been distributed.  The player of the role is normally a person or animal, but it is possible that other entity types could become exposed.  The role is scoped by the source of the exposure, and it is quite possible for a person playing the role of exposed party to also become the scoper a role played by another person.  That is to say, once a person has become infected, it is possible, perhaps likely, for that person to infect others.
   *
   *                         Management of exposures and tracking exposed parties is a key function within public health, and within most public health contexts - exposed parties are known as "contacts."
   */
  ExposedEntity: v3_RoleClass_ExposedEntity,
  /**
   * Entity that is currently in the possession of a holder (scoper), who holds, or uses it, usually based on some agreement with the owner.
   */
  HeldEntity: v3_RoleClass_HeldEntity,
  /**
   * The role of a material (player) that is the physical health chart belonging to an organization (scoper).
   */
  HealthChart: v3_RoleClass_HealthChart,
  /**
   * A role in which the scoping entity designates an identifier for a playing entity.
   */
  IdentifiedEntity: v3_RoleClass_IdentifiedEntity,
  /**
   * A role played by a place at which health care services may be provided without prior designation or authorization.
   */
  IncidentalServiceDeliveryLocation: v3_RoleClass_IncidentalServiceDeliveryLocation,
  /**
   * Scoped by the manufacturer
   */
  ManufacturedProduct: v3_RoleClass_ManufacturedProduct,
  /**
   * An entity (player) that is maintained by another entity (scoper).  This is typical role held by durable equipment. The scoper assumes responsibility for proper operation, quality, and safety.
   */
  MaintainedEntity: v3_RoleClass_MaintainedEntity,
  /**
   * An Entity (player) for which someone (scoper) is granted by law the right to call the material (player) his own.  This entitles the scoper to make decisions about the disposition of that material.
   */
  OwnedEntity: v3_RoleClass_OwnedEntity,
  /**
   * Material (player) sold by a retailer (scoper), who also give advice to prospective buyers.
   */
  RetailedMaterial: v3_RoleClass_RetailedMaterial,
  /**
   * A product regulated by some governmentatl orgnization.  The role is played by Material and scoped by Organization.
   *
   *                         Rationale: To support an entity clone used to identify the NDC number for a drug product.
   */
  RegulatedProduct: v3_RoleClass_RegulatedProduct,
  /**
   * A role played by a place at which services may be provided.
   */
  ServiceDeliveryLocation: v3_RoleClass_ServiceDeliveryLocation,
  /**
   * Relates a place entity (player) as the region over which the scoper (typically an Organization) has certain authority (jurisdiction). For example, the Calgary Regional Health Authority (scoper) has authority over the territory "Region 4 of Alberta" (player) in matters of health.
   */
  TerritoryOfAuthority: v3_RoleClass_TerritoryOfAuthority,
  /**
   * A manufactured material (player) that is used for its therapeutic properties.  The manufacturer is the scoper.
   */
  TherapeuticAgent: v3_RoleClass_TherapeuticAgent,
  /**
   * Description:An entity (player) that is used by another entity (scoper)
   */
  UsedEntity: v3_RoleClass_UsedEntity,
  /**
   * A role a product plays when a guarantee is given to the purchaser by the seller (scoping entity) stating that the product is reliable and free from known defects and that the seller will repair or replace defective parts within a given time limit and under certain conditions.
   */
  WarrantedProduct: v3_RoleClass_WarrantedProduct,
};
/**
 *  A relationship between two entities that is formally recognized, frequently by a contract or similar agreement.
 */
export const V3RoleClassRelationshipFormal = {
  /**
   * Player of the Affiliate role has a business/professional relationship with scoper.  Player and scoper may be persons or organization.  The Affiliate relationship does not imply membership in a group, nor does it exist for resource scheduling purposes.
   *
   *
   *                            Example: A healthcare provider is affiliated with another provider as a business associate.
   */
  Affiliate: v3_RoleClass_Affiliate,
  /**
   * An entity (player) that acts or is authorized to act on behalf of another entity (scoper).
   */
  Agent: v3_RoleClass_Agent,
  /**
   * An agent role in which the agent is an Entity acting in the employ of an organization.  The focus is on functional role on behalf of the organization, unlike the Employee role where the focus is on the 'Human Resources' relationship between the employee and the organization.
   */
  AssignedEntity: v3_RoleClass_AssignedEntity,
  /**
   * A person, non-person living subject, or place that is the subject of an investigation related to a notifiable condition (health circumstance that is reportable within the applicable public health jurisdiction)
   */
  CaseSubject: v3_RoleClass_CaseSubject,
  /**
   * Citizen of apolitical entity
   */
  Citizen: v3_RoleClass_Citizen,
  /**
   * Description: A role played by a party making a claim for coverage under a policy or program.  A claimant must be either a person or organization, or a group of persons or organizations.  A claimant is not a named insured or a program eligible.
   *
   *
   *                            Discussion: With respect to liability insurance such as property and casualty insurance, a claimant must file a claim requesting indemnification for a loss that the claimant considers covered under the policy of a named insured.  The claims adjuster for the policy underwriter will review the claim to determine whether the loss meets the benefit coverage criteria under a policy, and base any indemnification or coverage payment on that review.  If a third party is liable in whole or part for the loss, the underwriter may pursue third party liability recovery.  A claimant may be involved in civil or criminal legal proceedings involving claims against a defendant party that is indemnified by an insurance policy or to protest the finding of a claims adjustor. With respect to life insurance, a beneficiary designated by a named insured becomes a claimant of the proceeds of coverage, as in the case of a life insurance policy.  However, a claimant for coverage under life insurance is not necessarily a designated beneficiary.
   *
   *
   *                            Note: A claimant is not a named insured.  However, a named insured may make a claim under a policy, e.g., an insured driver may make a claim for an injury under his or her comprehensive automobile insurance policy.  Similarly, a program eligible may make a claim under program, e.g., an unemployed worker may claim benefits under an unemployment insurance program, but parties playing these covered party role classes are not, for purposes of this vocabulary and in an effort to clearly distinguish role classes, considered claimants.
   *
   *                         In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that either a named insured or an individual insured has filed a claim for a loss.  In the case of a program eligible, a role type code INJWKR (injured worker) subtypes the class to indicate that the covered party in a workers compensation program is an injured worker, and as such, has filed a "claim" under the program for benefits.  Likewise, a covered role type code UNEMP (unemployed worker) subtypes the program eligible class to indicate that the covered party in an unemployment insurance program has filed a claim for unemployment benefits.
   *
   *
   *                            Example: A claimant under automobile policy that is not the named insured.
   */
  Claimant: v3_RoleClass_Claimant,
  /**
   * An Entity that is authorized to issue or instantiate permissions, privileges, credentials or other formal/legal authorizations.
   */
  CommissioningParty: v3_RoleClass_CommissioningParty,
  /**
   * A person or an organization (player) which provides or receives information regarding another entity (scoper).  Examples; patient NOK and emergency contacts; guarantor contact; employer contact.
   */
  Contact: v3_RoleClass_Contact,
  /**
   * A role class played by a person who receives benefit coverage under the terms of a particular insurance policy.  The underwriter of that policy is the scoping entity.  The covered party receives coverage because of some contractual or other relationship with the holder of that policy.
   *
   *
   *                            Discussion:This reason for coverage is captured in 'Role.code' and a relationship link with type code of indirect authority should be included using the policy holder role as the source, and the covered party role as the target.
   *
   *                         Note that a particular policy may cover several individuals one of whom may be, but need not be, the policy holder.  Thus the notion of covered party is a role that is distinct from that of the policy holder.
   */
  CoveredParty: v3_RoleClass_CoveredParty,
  /**
   * A role played by a provider, always a person, who has agency authority from a Clinical Research Sponsor to direct the conduct of a clinical research trial or study on behalf of the sponsor.
   */
  ClinicalResearchInvestigator: v3_RoleClass_ClinicalResearchInvestigator,
  /**
   * A role played by an entity, usually an organization, that is the sponsor of a clinical research trial or study.  The sponsor commissions the study, bears the expenses, is responsible for satisfying all legal requirements concerning subject safety and privacy, and is generally responsible for collection, storage and analysis of the data generated during the trial.  No scoper is necessary, as a clinical research sponsor undertakes the role on its own authority and declaration. Clinical research sponsors are usually educational or other research organizations, government agencies or biopharmaceutical companies.
   */
  ClinicalResearchSponsor: v3_RoleClass_ClinicalResearchSponsor,
  /**
   * Description: A role played by a person covered under a policy or program based on an association with a subscriber, which is recognized by the policy holder.
   *
   *
   *                            Note:  The party playing the role of a dependent is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a dependent may make a claim under a policy, e.g., a dependent under a health insurance policy may become the claimant for coverage under that policy for wellness examines or if injured and there is no liable third party.  In the case of a dependent making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that the dependent has filed a claim for services covered under the health insurance policy.
   *
   *
   *                            Example: The dependent has an association with the subscriber such as a financial dependency or personal relationship such as that of a spouse, or a natural or adopted child.  The policy holder may be required by law to recognize certain associations or may have discretion about the associations.  For example, a policy holder may dictate the criteria for the dependent status of adult children who are students, such as requiring full time enrollment, or may recognize domestic partners as dependents.  Under certain circumstances, the dependent may be under the indirect authority of a responsible party acting as a surrogate for the subscriber, for example, if the subscriber is differently abled or deceased, a guardian ad Lidem or estate executor may be appointed to assume the subscriberaTMs legal standing in the relationship with the dependent.
   */
  Dependent: v3_RoleClass_Dependent,
  /**
   * An entity to be contacted in the event of an emergency.
   */
  EmergencyContact: v3_RoleClass_EmergencyContact,
  /**
   * A relationship between a person or organization and a person or organization formed for the purpose of exchanging work for compensation.  The purpose of the role is to identify the type of relationship the employee has to the employer, rather than the nature of the work actually performed.  (Contrast with AssignedEntity.)
   */
  Employee: v3_RoleClass_Employee,
  /**
   * A person or organization (player) that serves as a financial guarantor for another person or organization (scoper).
   */
  Guarantor: v3_RoleClass_Guarantor,
  /**
   * Guardian of a ward
   */
  Guardian: v3_RoleClass_Guardian,
  /**
   * Description: A role played by a party covered under a policy as the policy holder.  An individual may be either a person or an organization.
   *
   *
   *                            Note: The party playing the role of an individual insured is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a named insured may make a claim under a policy, e.g., a party that is the named insured and policy holder under a comprehensive automobile insurance policy may become the claimant for coverage under that policy if injured in an automobile accident and there is no liable third party.  In the case of an individual insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that an individual insured has filed a claim for a loss.
   *
   *
   *                            Example: The individual insured under a comprehensive automobile, disability, or property and casualty policy that is the policy holder.
   */
  Individual: v3_RoleClass_Individual,
  /**
   * An entity that is the subject of an investigation. This role is scoped by the party responsible for the investigation.
   */
  InvestigationSubject: v3_RoleClass_InvestigationSubject,
  /**
   * A relationship in which the scoper certifies the player ( e. g. a medical care giver, a medical device or a provider organization) to perform certain activities that fall under the jurisdiction of the scoper (e.g., a health authority licensing healthcare providers, a medical quality authority certifying healthcare professionals).
   */
  LicensedEntity: v3_RoleClass_LicensedEntity,
  /**
   * A role played by a member of a military service. Scoper is the military service (e.g. Army, Navy, Air Force, etc.) or, more specifically, the unit (e.g. Company C, 3rd Battalion, 4th Division, etc.)
   */
  MilitaryPerson: v3_RoleClass_MilitaryPerson,
  /**
   * Description: A role played by a party to an insurance policy to which the insurer agrees to indemnify for losses, provides benefits for, or renders services.  A named insured may be either a person, non-person living subject, or an organization, or a group of persons, non-person living subjects, or organizations.
   *
   *
   *                            Discussion: The coded concept NAMED should not be used where a more specific child concept in this Specializable value set applies.  In some cases, the named insured may not be the policy holder, e.g., where a policy holder purchases life insurance policy in which another party is the named insured and the policy holder is the beneficiary of the policy.
   *
   *
   *                            Note: The party playing the role of a named insured is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a named insured may make a claim under a policy, e.g., e.g., a party that is the named insured and policy holder under a comprehensive automobile insurance policy may become the claimant for coverage under that policy e.g., if injured in an automobile accident and there is no liable third party.  In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that a named insured has filed a claim for a loss.
   *
   *
   *                            Example: The named insured under a comprehensive automobile, disability, or property and casualty policy that is the named insured and may or may not be the policy holder.
   */
  NamedInsured: v3_RoleClass_NamedInsured,
  /**
   * An individual designated for notification as the next of kin for a given entity.
   */
  NextOfKin: v3_RoleClass_NextOfKin,
  /**
   * notary public
   */
  NotaryPublic: v3_RoleClass_NotaryPublic,
  /**
   * A Role of a LivingSubject (player) as an actual or potential recipient of health care services from a healthcare provider organization (scoper).
   *
   *
   *                            Usage Note: Communication about relationships between patients and specific healthcare practitioners (people) is not done via scoper.  Instead this is generally done using the CareProvision act.  This allows linkage between patient and a particular healthcare practitioner role and also allows description of the type of care involved in the relationship.
   */
  Patient: v3_RoleClass_Patient,
  /**
   * The role of an organization or individual designated to receive payment for a claim against a particular coverage. The scoping entity is the organization that is the submitter of the invoice in question.
   */
  Payee: v3_RoleClass_Payee,
  /**
   * The role of an organization that undertakes to accept claims invoices, assess the coverage or payments due for those invoices and pay to the designated payees for those invoices.  This role may be either the underwriter or a third-party organization authorized by the underwriter.  The scoping entity is the organization that underwrites the claimed coverage.
   */
  InvoicePayor: v3_RoleClass_InvoicePayor,
  /**
   * A role played by a person or organization that holds an insurance policy.  The underwriter of that policy is the scoping entity.
   *
   *
   *                            Discussion:The identifier of the policy is captured in 'Role.id' when the Role is a policy holder.
   *
   *                         A particular policy may cover several individuals one of whom may be, but need not be, the policy holder.  Thus the notion of covered party is a role that is distinct from that of the policy holder.
   */
  PolicyHolder: v3_RoleClass_PolicyHolder,
  /**
   * Description: A role played by a party that meets the eligibility criteria for coverage under a program.  A program eligible may be either a person, non-person living subject, or an organization, or a group of persons, non-person living subjects, or organizations.
   *
   *
   *                            Discussion: A program as typically government administered coverage for parties determined eligible under the terms of the program.
   *
   *
   *                            Note: The party playing a program eligible is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However a program eligible may make a claim under program, e.g., an unemployed worker may claim benefits under an unemployment insurance program, but parties playing these covered party role classes are not, for purposes of this vocabulary and in an effort to clearly distinguish role classes, considered claimants.
   *
   *                         In the case of a program eligible, a role type code INJWKR (injured worker) subtypes the class to indicate that the covered party in a workers compensation program is an injured worker, and as such, has filed a "claim" under the program for benefits.  Likewise, a covered role type code UNEMP (unemployed worker) subtypes the program eligible class to indicate that the covered party in an unemployment insurance program has filed a claim for unemployment benefits.
   *
   *
   *                            Example: A party meeting eligibility criteria related to health or financial status, e.g., in the U.S., persons meeting health, demographic, or financial criteria established by state and federal law are eligible for Medicaid.
   */
  ProgramEligible: v3_RoleClass_ProgramEligible,
  /**
   * An Entity (player) that is authorized to provide health care services by some authorizing agency (scoper).
   */
  HealthcareProvider: v3_RoleClass_HealthcareProvider,
  /**
   * An entity (player) that has been recognized as having certain training/experience or other characteristics that would make said entity an appropriate performer for a certain activity. The scoper is an organization that educates or qualifies entities.
   */
  QualifiedEntity: v3_RoleClass_QualifiedEntity,
  /**
   * Definition:Specifies the administrative functionality within a formal experimental design for which the ResearchSubject role was established.
   *
   *
   *                            Examples: Screening - role is used for pre-enrollment evaluation portion of the design; enrolled - role is used for subjects admitted to the experimental portion of the design.
   */
  ResearchSubject: v3_RoleClass_ResearchSubject,
  /**
   * The role of a person (player) who is the officer or signature authority for of a scoping entity, usually an organization (scoper).
   */
  SigningAuthorityOrOfficer: v3_RoleClass_SigningAuthorityOrOfficer,
  /**
   * A role played by an entity, usually an organization that is the sponsor of an insurance plan or a health program. A sponsor is the party that is ultimately accountable for the coverage by employment contract or by law.  A sponsor can be an employer, union, government agency, or association.  Fully insured sponsors establish the terms of the plan and contract with health insurance plans to assume the risk and to administer the plan.  Self-insured sponsors delegate coverage administration, but not risk, to third-party administrators.  Program sponsors designate services to be covered in accordance with statute.   Program sponsors may administer the coverage themselves, delegate coverage administration, but not risk to third-party administrators, or contract with health insurance plans to assume the risk and administrator a program. Sponsors qualify individuals who may become
   *
   *
   *
   *                               a policy holder of the plan;
   *
   *
   *
   *                               where the sponsor is the policy holder, who may become a subscriber or a dependent to a policy under the plan; or
   *
   *
   *
   *                               where the sponsor is a government agency, who may become program eligibles under a program.
   *
   *
   *
   *                         The sponsor role may be further qualified by the SponsorRole.code.  Entities playing the sponsor role may also play the role of a Coverage Administrator.
   *
   *
   *                            Example: An employer, union, government agency, or association.
   */
  CoverageSponsor: v3_RoleClass_CoverageSponsor,
  /**
   * A role played by an individual who is a student of a school, which is the scoping entity.
   */
  Student: v3_RoleClass_Student,
  /**
   * Description: A role played by a person covered under a policy based on association with a sponsor who is the policy holder, and whose association may provide for the eligibility of dependents for coverage.
   *
   *
   *                            Discussion: The policy holder holds the contract with the policy or program underwriter.  The subscriber holds a certificate of coverage under the contract.  In legal proceedings concerning the policy or program, the terms of the contract takes precedence over the terms of the certificate of coverage if there are any inconsistencies.
   *
   *
   *                            Note: The party playing the role of a subscriber is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a subscriber may make a claim under a policy, e.g., a subscriber under a health insurance policy may become the claimant for coverage under that policy for wellness examines or if injured and there is no liable third party.  In the case of a subscriber making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that the subscriber has filed a claim for services covered under the health insurance policy.
   *
   *
   *                            Example: An employee or a member of an association.
   */
  Subscriber: v3_RoleClass_Subscriber,
  /**
   * A role played by a person or an organization.  It is the party that
   *
   *
   *
   *                               accepts fiscal responsibility for insurance plans and the policies created under those plans;
   *
   *
   *
   *                               administers and accepts fiscal responsibility for a program that provides coverage for services to eligible individuals; and/or
   *
   *
   *
   *                               has the responsibility to assess the merits of each risk and decide a suitable premium for accepting all or part of the risk.  If played by an organization, this role may be further specified by an appropriate RoleCode.
   *
   *
   *
   *
   *                            Example:
   *
   *
   *
   *
   *                               A health insurer;
   *
   *
   *
   *                               Medicaid Program;
   *
   *
   *
   *                               Lloyd's of London
   */
  Underwriter: v3_RoleClass_Underwriter,
};
/**
 *  Corresponds to the Role class
 */
export const V3RoleClassRoot = {
  /**
   * A general association between two entities that is neither partitive nor ontological.
   */
  RoleClassAssociative: v3_RoleClass_RoleClassAssociative,
  /**
   * A relationship that is based on mutual behavior of the two Entities as being related. The basis of such relationship may be agreements (e.g., spouses, contract parties) or they may be de facto behavior (e.g. friends) or may be an incidental involvement with each other (e.g. parties over a dispute, siblings, children).
   */
  RoleClassMutualRelationship: v3_RoleClass_RoleClassMutualRelationship,
  /**
   * A relationship in which the scoping Entity defines or specifies what the playing Entity is.  Thus, the player's "being" (Greek: ontos) is specified.
   */
  RoleClassOntological: v3_RoleClass_RoleClassOntological,
  /**
   * An association between two Entities where the playing Entity is considered in some way "part" of the scoping Entity, e.g., as a member, component, ingredient, or content. Being "part" in the broadest sense of the word can mean anything from being an integral structural component to a mere incidental temporary association of a playing Entity with a (generally larger) scoping Entity.
   */
  RoleClassPartitive: v3_RoleClass_RoleClassPartitive,
  /**
   * An association for a playing Entity that is used, known, treated, handled, built, or destroyed, etc. under the auspices of the scoping Entity. The playing Entity is passive in these roles (even though it may be active in other roles), in the sense that the kinds of things done to it in this role happen without an agreement from the playing Entity.
   */
  RoleClassPassive: v3_RoleClass_RoleClassPassive,
  /**
   * A relationship between two entities that is formally recognized, frequently by a contract or similar agreement.
   */
  RoleClassRelationshipFormal: v3_RoleClass_RoleClassRelationshipFormal,
  /**
   * A role in which the playing entity (material) provides access to another entity. The principal use case is intravenous (or other bodily) access lines that preexist and need to be referred to for medication routing instructions.
   */
  Access: v3_RoleClass_Access,
  /**
   * Definition: a therapeutically active ingredient (player) in a mixture (scoper), where the mixture is typically a manufactured pharmaceutical.  It is unknown if the quantity of such an ingredient is expressed precisely in terms of the playing ingredient substance, or, if it is specified in terms of a closely related substance (active moiety or reference substance).
   */
  ActiveIngredient: v3_RoleClass_ActiveIngredient,
  /**
   * Description:  Active ingredient, where the ingredient substance (player) is itself the "basis of strength", i.e., where the Role.quantity specifies exactly the quantity of the player substance in the medicine formulation.
   *
   *
   *                            Examples: Lopressor 50 mg actually contains 50 mg of metoprolol succinate, even though the active moiety is metoprolol,  but also: Tenormin 50 mg contain 50 mg of atenolol, as free base, i.e., where the active ingredient atenolol is also the active moiety.
   */
  ActiveIngredientBasisOfStrength: v3_RoleClass_ActiveIngredientBasisOfStrength,
  /**
   * Description: Active ingredient, where not the ingredient substance (player), but itaTMs active moiety is the "basis of strength", i.e., where the Role.quantity specifies the quantity of the player substance's active moiety in the medicine formulation.
   *
   *
   *                            Examples: 1 mL of Betopic 5mg/mL eye drops contains 5.6 mg betaxolol hydrochloride equivalent to betaxolol base 5 mg.
   */
  ActiveIngredientMoietyIsBasisOfStrength: v3_RoleClass_ActiveIngredientMoietyIsBasisOfStrength,
  /**
   * Description: Active ingredient, where not the ingredient substance (player) but another reference substance with the same active moiety, is the "basis of strength", i.e., where the Role.quantity specifies the quantity of a reference substance, similar but different from the player substance's in the medicine formulation.
   *
   *
   *                            Examples: Toprol-XL 50 mg contains 47.5 mg of metoprolol succinate equivalent to 50 mg of metoprolol tartrate.
   */
  ActiveIngredientReferenceSubstanceIsBasisOfStrength: v3_RoleClass_ActiveIngredientReferenceSubstanceIsBasisOfStrength,
  /**
   * The molecule or ion that is responsible for the intended pharmacological action of the drug substance, excluding those appended or associated parts of the molecule that make the molecule an ester, salt (including a salt with hydrogen or coordination bonds), or other noncovalent derivative (such as a complex, chelate, or clathrate).
   *
   *                         Examples: heparin-sodium and heparin-potassium have the same active moiety, heparin; the active moiety of morphine-hydrochloride is morphine.
   */
  ActiveMoiety: v3_RoleClass_ActiveMoiety,
  /**
   * A component (player) added to enhance the action of an active ingredient (scoper) (in the manner of a catalyst) but which has no active effect in and of itself.  Such ingredients are significant in defining equivalence of products in a way that inactive ingredients are not.
   */
  Adjuvant: v3_RoleClass_Adjuvant,
  /**
   * A physical association whereby two Entities are in some (even lose) spatial relationship with each other such that they touch each other in some way.
   *
   *
   *                            Examples: the colon is connected (and therefore adjacent) to the jejunum; the colon is adjacent to the liver (even if not actually connected.)
   *
   *
   *                            UsageConstraints: Adjacency is in principle a symmetrical connection, but scoper and player of the role should, where applicable, be assigned to have scoper be the larger, more central Entity and player the smaller, more distant, appendage.
   */
  Adjacency: v3_RoleClass_Adjacency,
  /**
   * A material (player) that can be administered to an Entity (scoper).
   */
  AdministerableMaterial: v3_RoleClass_AdministerableMaterial,
  /**
   * An ingredient (player)  that is added to a base (scoper), that amounts to a minor part of the overall mixture.
   */
  Additive: v3_RoleClass_Additive,
  /**
   * Player of the Affiliate role has a business/professional relationship with scoper.  Player and scoper may be persons or organization.  The Affiliate relationship does not imply membership in a group, nor does it exist for resource scheduling purposes.
   *
   *
   *                            Example: A healthcare provider is affiliated with another provider as a business associate.
   */
  Affiliate: v3_RoleClass_Affiliate,
  /**
   * An entity (player) that acts or is authorized to act on behalf of another entity (scoper).
   */
  Agent: v3_RoleClass_Agent,
  /**
   * A portion (player) of an original or source specimen (scoper) used for testing or transportation.
   */
  Aliquot: v3_RoleClass_Aliquot,
  /**
   * An agent role in which the agent is an Entity acting in the employ of an organization.  The focus is on functional role on behalf of the organization, unlike the Employee role where the focus is on the 'Human Resources' relationship between the employee and the organization.
   */
  AssignedEntity: v3_RoleClass_AssignedEntity,
  /**
   * A base ingredient (player) is what comprises the major part of a mixture (scoper). E.g., Water in most i.v. solutions, or Vaseline in salves. Among all ingredients of a material, there should be only one base. A base substance can, in turn, be a mixture.
   */
  Base: v3_RoleClass_Base,
  /**
   * Relates a place (playing Entity) as the location where a living subject (scoping Entity) was born.
   */
  Birthplace: v3_RoleClass_Birthplace,
  /**
   * A connection between two atoms of a molecule.
   *
   *
   *                            Examples: double bond between first and second C in ethane, peptide bond between two amino-acid, disulfide bridge between two proteins, chelate and ion associations, even the much weaker van-der-Waals bonds can be considered molecular bonds.
   *
   *
   *                            UsageConstraints: See connection and adjacency for the assignment of player and scoper.
   */
  MolecularBond: v3_RoleClass_MolecularBond,
  /**
   * A person responsible for the primary care of a patient at home.
   */
  Caregiver: v3_RoleClass_Caregiver,
  /**
   * A person, non-person living subject, or place that is the subject of an investigation related to a notifiable condition (health circumstance that is reportable within the applicable public health jurisdiction)
   */
  CaseSubject: v3_RoleClass_CaseSubject,
  /**
   * Citizen of apolitical entity
   */
  Citizen: v3_RoleClass_Citizen,
  /**
   * Description: A role played by a party making a claim for coverage under a policy or program.  A claimant must be either a person or organization, or a group of persons or organizations.  A claimant is not a named insured or a program eligible.
   *
   *
   *                            Discussion: With respect to liability insurance such as property and casualty insurance, a claimant must file a claim requesting indemnification for a loss that the claimant considers covered under the policy of a named insured.  The claims adjuster for the policy underwriter will review the claim to determine whether the loss meets the benefit coverage criteria under a policy, and base any indemnification or coverage payment on that review.  If a third party is liable in whole or part for the loss, the underwriter may pursue third party liability recovery.  A claimant may be involved in civil or criminal legal proceedings involving claims against a defendant party that is indemnified by an insurance policy or to protest the finding of a claims adjustor. With respect to life insurance, a beneficiary designated by a named insured becomes a claimant of the proceeds of coverage, as in the case of a life insurance policy.  However, a claimant for coverage under life insurance is not necessarily a designated beneficiary.
   *
   *
   *                            Note: A claimant is not a named insured.  However, a named insured may make a claim under a policy, e.g., an insured driver may make a claim for an injury under his or her comprehensive automobile insurance policy.  Similarly, a program eligible may make a claim under program, e.g., an unemployed worker may claim benefits under an unemployment insurance program, but parties playing these covered party role classes are not, for purposes of this vocabulary and in an effort to clearly distinguish role classes, considered claimants.
   *
   *                         In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that either a named insured or an individual insured has filed a claim for a loss.  In the case of a program eligible, a role type code INJWKR (injured worker) subtypes the class to indicate that the covered party in a workers compensation program is an injured worker, and as such, has filed a "claim" under the program for benefits.  Likewise, a covered role type code UNEMP (unemployed worker) subtypes the program eligible class to indicate that the covered party in an unemployment insurance program has filed a claim for unemployment benefits.
   *
   *
   *                            Example: A claimant under automobile policy that is not the named insured.
   */
  Claimant: v3_RoleClass_Claimant,
  /**
   * An ingredient whose presence is not intended but may not be reasonably avoided given the circumstances of the mixture's nature or origin.
   */
  ContaminantIngredient: v3_RoleClass_ContaminantIngredient,
  /**
   * A substance (player) influencing the optical aspect of material (scoper).
   */
  ColorAdditive: v3_RoleClass_ColorAdditive,
  /**
   * An Entity that is authorized to issue or instantiate permissions, privileges, credentials or other formal/legal authorizations.
   */
  CommissioningParty: v3_RoleClass_CommissioningParty,
  /**
   * A person or an organization (player) which provides or receives information regarding another entity (scoper).  Examples; patient NOK and emergency contacts; guarantor contact; employer contact.
   */
  Contact: v3_RoleClass_Contact,
  /**
   * An adjacency of two Entities held together by a bond which attaches to each of the two entities.
   *
   *
   *                            Examples: biceps brachii muscle connected to the radius bone, port 3 on a network switch connected to port 5 on a patch panel.
   *
   *
   *                            UsageConstraints: See Adjacency for the assignment of scoper (larger, more central) and player (smaller, more distant).
   */
  Connection: v3_RoleClass_Connection,
  /**
   * Relates a material as the content (player) to a container (scoper).  Unlike ingredients, the content and a container remain separate (not mixed) and the content can be removed from the container.  A content is not part of an empty container.
   */
  Content: v3_RoleClass_Content,
  /**
   * A connection between two regional parts.
   *
   *
   *                            Examples:  the connection between ascending aorta and the aortic arc, connection between descending colon and sigmoid.
   *
   *
   *                            UsageConstraints: See connection and adjacency for the assignment of player and scoper.
   */
  Continuity: v3_RoleClass_Continuity,
  /**
   * A role class played by a person who receives benefit coverage under the terms of a particular insurance policy.  The underwriter of that policy is the scoping entity.  The covered party receives coverage because of some contractual or other relationship with the holder of that policy.
   *
   *
   *                            Discussion:This reason for coverage is captured in 'Role.code' and a relationship link with type code of indirect authority should be included using the policy holder role as the source, and the covered party role as the target.
   *
   *                         Note that a particular policy may cover several individuals one of whom may be, but need not be, the policy holder.  Thus the notion of covered party is a role that is distinct from that of the policy holder.
   */
  CoveredParty: v3_RoleClass_CoveredParty,
  /**
   * A role played by a provider, always a person, who has agency authority from a Clinical Research Sponsor to direct the conduct of a clinical research trial or study on behalf of the sponsor.
   */
  ClinicalResearchInvestigator: v3_RoleClass_ClinicalResearchInvestigator,
  /**
   * A role played by an entity, usually an organization, that is the sponsor of a clinical research trial or study.  The sponsor commissions the study, bears the expenses, is responsible for satisfying all legal requirements concerning subject safety and privacy, and is generally responsible for collection, storage and analysis of the data generated during the trial.  No scoper is necessary, as a clinical research sponsor undertakes the role on its own authority and declaration. Clinical research sponsors are usually educational or other research organizations, government agencies or biopharmaceutical companies.
   */
  ClinicalResearchSponsor: v3_RoleClass_ClinicalResearchSponsor,
  /**
   * Definition: Relates a place (playing Entity) as the location where a living subject (scoping Entity) died.
   */
  PlaceOfDeath: v3_RoleClass_PlaceOfDeath,
  /**
   * Description: A role played by a person covered under a policy or program based on an association with a subscriber, which is recognized by the policy holder.
   *
   *
   *                            Note:  The party playing the role of a dependent is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a dependent may make a claim under a policy, e.g., a dependent under a health insurance policy may become the claimant for coverage under that policy for wellness examines or if injured and there is no liable third party.  In the case of a dependent making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that the dependent has filed a claim for services covered under the health insurance policy.
   *
   *
   *                            Example: The dependent has an association with the subscriber such as a financial dependency or personal relationship such as that of a spouse, or a natural or adopted child.  The policy holder may be required by law to recognize certain associations or may have discretion about the associations.  For example, a policy holder may dictate the criteria for the dependent status of adult children who are students, such as requiring full time enrollment, or may recognize domestic partners as dependents.  Under certain circumstances, the dependent may be under the indirect authority of a responsible party acting as a surrogate for the subscriber, for example, if the subscriber is differently abled or deceased, a guardian ad Lidem or estate executor may be appointed to assume the subscriberaTMs legal standing in the relationship with the dependent.
   */
  Dependent: v3_RoleClass_Dependent,
  /**
   * A role of a place (player) that is intended to house the provision of services. Scoper is the Entity (typically Organization) that provides these services. This is not synonymous with "ownership."
   */
  DedicatedServiceDeliveryLocation: v3_RoleClass_DedicatedServiceDeliveryLocation,
  /**
   * A material (player) distributed by a distributor (scoper) who functions between a manufacturer and a buyer or retailer.
   */
  DistributedMaterial: v3_RoleClass_DistributedMaterial,
  /**
   * An entity to be contacted in the event of an emergency.
   */
  EmergencyContact: v3_RoleClass_EmergencyContact,
  /**
   * A relationship between a person or organization and a person or organization formed for the purpose of exchanging work for compensation.  The purpose of the role is to identify the type of relationship the employee has to the employer, rather than the nature of the work actually performed.  (Contrast with AssignedEntity.)
   */
  Employee: v3_RoleClass_Employee,
  /**
   * Description: Specifies the player Entity (the equivalent Entity) as an Entity that is considered to be equivalent to a reference Entity (scoper).  The equivalence is in principle a symmetric relationship, however, it is expected that the scoper is a reference entity which serves as reference entity for multiple different equivalent entities.
   *
   *
   *                            Examples: An innovator's medicine formulation is the reference for "generics", i.e., formulations manufactured differently but having been proven to be biologically equivalent to the reference medicine. Another example is a reference ingredient that serves as basis for quantity specifications (basis of strength, e.g., metoprolol succinate specified in terms of metoprolol tartrate.)
   */
  EquivalentEntity: v3_RoleClass_EquivalentEntity,
  /**
   * A role played by a place at which the location of an event may be recorded.
   */
  EventLocation: v3_RoleClass_EventLocation,
  /**
   * An exposure agent carrier is an entity that is capable of conveying an exposure agent from one entity to another.  The scoper of the role must be the exposure agent (e.g., pathogen).
   */
  ExposureAgentCarrier: v3_RoleClass_ExposureAgentCarrier,
  /**
   * A role played by an entity that has been exposed to a person or animal suffering a contagious disease, or with a location from which a toxin has been distributed.  The player of the role is normally a person or animal, but it is possible that other entity types could become exposed.  The role is scoped by the source of the exposure, and it is quite possible for a person playing the role of exposed party to also become the scoper a role played by another person.  That is to say, once a person has become infected, it is possible, perhaps likely, for that person to infect others.
   *
   *                         Management of exposures and tracking exposed parties is a key function within public health, and within most public health contexts - exposed parties are known as "contacts."
   */
  ExposedEntity: v3_RoleClass_ExposedEntity,
  /**
   * Description: A vector is a living subject that carries an exposure agent.  The vector does not cause the disease itself, but exposes targets to the exposure agent.  A mosquito carrying malaria is an example of a vector.  The scoper of the role must be the exposure agent (e.g., pathogen).
   */
  ExposureVector: v3_RoleClass_ExposureVector,
  /**
   * A substance (player) added to a mixture (scoper) to make it taste a certain way.  In food the use is obvious, in pharmaceuticals flavors can hide disgusting taste of the active ingredient (important in pediatric treatments).
   */
  FlavorAdditive: v3_RoleClass_FlavorAdditive,
  /**
   * Description: A fomite is a non-living entity that is capable of conveying exposure agent from one entity to another.  A doorknob contaminated with a Norovirus is an example of a fomite.  Anyone touching the doorknob would be exposed to the virus.  The scoper of the role must be the exposure agent (e.g., pathogen).
   */
  Fomite: v3_RoleClass_Fomite,
  /**
   * Relates a specialized material concept (player) to its generalization (scoper).
   */
  HasGeneralization: v3_RoleClass_HasGeneralization,
  /**
   * A special link between pharmaceuticals indicating that the target (scoper) is a generic for the source (player).
   */
  HasGeneric: v3_RoleClass_HasGeneric,
  /**
   * A person or organization (player) that serves as a financial guarantor for another person or organization (scoper).
   */
  Guarantor: v3_RoleClass_Guarantor,
  /**
   * Guardian of a ward
   */
  Guardian: v3_RoleClass_Guardian,
  /**
   * Entity that is currently in the possession of a holder (scoper), who holds, or uses it, usually based on some agreement with the owner.
   */
  HeldEntity: v3_RoleClass_HeldEntity,
  /**
   * The role of a material (player) that is the physical health chart belonging to an organization (scoper).
   */
  HealthChart: v3_RoleClass_HealthChart,
  /**
   * An ingredient which is not considered therapeutically active, e.g., colors, flavors, stabilizers, or preservatives, fillers, or structural components added to an active ingredient in order to facilitate administration of the active ingredient but without being considered therapeutically active. An inactive ingredient need not be biologically inert, e.g., might be active as an allergen or might have a pleasant taste, but is not an essential constituent delivering the therapeutic effect.
   */
  InactiveIngredient: v3_RoleClass_InactiveIngredient,
  /**
   * A role in which the scoping entity designates an identifier for a playing entity.
   */
  IdentifiedEntity: v3_RoleClass_IdentifiedEntity,
  /**
   * Description: A role played by a party covered under a policy as the policy holder.  An individual may be either a person or an organization.
   *
   *
   *                            Note: The party playing the role of an individual insured is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a named insured may make a claim under a policy, e.g., a party that is the named insured and policy holder under a comprehensive automobile insurance policy may become the claimant for coverage under that policy if injured in an automobile accident and there is no liable third party.  In the case of an individual insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that an individual insured has filed a claim for a loss.
   *
   *
   *                            Example: The individual insured under a comprehensive automobile, disability, or property and casualty policy that is the policy holder.
   */
  Individual: v3_RoleClass_Individual,
  /**
   * Relates a component (player) to a mixture (scoper). E.g., Glucose and Water are ingredients of D5W, latex may be an ingredient in a tracheal tube.
   */
  Ingredient: v3_RoleClass_Ingredient,
  /**
   * An individual piece of material (player) instantiating a class of material (scoper).
   */
  Instance: v3_RoleClass_Instance,
  /**
   * An entity that is the subject of an investigation. This role is scoped by the party responsible for the investigation.
   */
  InvestigationSubject: v3_RoleClass_InvestigationSubject,
  /**
   * A role played by a place at which health care services may be provided without prior designation or authorization.
   */
  IncidentalServiceDeliveryLocation: v3_RoleClass_IncidentalServiceDeliveryLocation,
  /**
   * A microorganism that has been isolated from other microorganisms or a source matrix.
   */
  Isolate: v3_RoleClass_Isolate,
  /**
   * A relationship in which the scoper certifies the player ( e. g. a medical care giver, a medical device or a provider organization) to perform certain activities that fall under the jurisdiction of the scoper (e.g., a health authority licensing healthcare providers, a medical quality authority certifying healthcare professionals).
   */
  LicensedEntity: v3_RoleClass_LicensedEntity,
  /**
   * Relates an entity (player) to a location (scoper) at which it is present in some way. This presence may be limited in time.
   */
  LocatedEntity: v3_RoleClass_LocatedEntity,
  /**
   * Scoped by the manufacturer
   */
  ManufacturedProduct: v3_RoleClass_ManufacturedProduct,
  /**
   * A role played by an entity that is a member of a group.  The group provides the scope for this role.
   *
   *                         Among other uses, groups as used in insurance (groups of covered individuals) and in scheduling where resources may be grouped for scheduling and logistical purposes.
   */
  Member: v3_RoleClass_Member,
  /**
   * An ingredient (player) of a medication (scoper) that is inseparable from the active ingredients, but has no intended chemical or pharmaceutical effect itself, but which may have some systemic effect on the patient.
   *
   *                         An example is a collagen matrix used as a base for transplanting skin cells.  The collagen matrix can be left permanently in the graft site.  Because it is of bovine origin, the patient may exhibit allergies or may have cultural objections to its use.
   */
  MechanicalIngredient: v3_RoleClass_MechanicalIngredient,
  /**
   * A role played by a member of a military service. Scoper is the military service (e.g. Army, Navy, Air Force, etc.) or, more specifically, the unit (e.g. Company C, 3rd Battalion, 4th Division, etc.)
   */
  MilitaryPerson: v3_RoleClass_MilitaryPerson,
  /**
   * An entity (player) that is maintained by another entity (scoper).  This is typical role held by durable equipment. The scoper assumes responsibility for proper operation, quality, and safety.
   */
  MaintainedEntity: v3_RoleClass_MaintainedEntity,
  /**
   * Description: A role played by a party to an insurance policy to which the insurer agrees to indemnify for losses, provides benefits for, or renders services.  A named insured may be either a person, non-person living subject, or an organization, or a group of persons, non-person living subjects, or organizations.
   *
   *
   *                            Discussion: The coded concept NAMED should not be used where a more specific child concept in this Specializable value set applies.  In some cases, the named insured may not be the policy holder, e.g., where a policy holder purchases life insurance policy in which another party is the named insured and the policy holder is the beneficiary of the policy.
   *
   *
   *                            Note: The party playing the role of a named insured is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a named insured may make a claim under a policy, e.g., e.g., a party that is the named insured and policy holder under a comprehensive automobile insurance policy may become the claimant for coverage under that policy e.g., if injured in an automobile accident and there is no liable third party.  In the case of a named insured making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that a named insured has filed a claim for a loss.
   *
   *
   *                            Example: The named insured under a comprehensive automobile, disability, or property and casualty policy that is the named insured and may or may not be the policy holder.
   */
  NamedInsured: v3_RoleClass_NamedInsured,
  /**
   * An individual designated for notification as the next of kin for a given entity.
   */
  NextOfKin: v3_RoleClass_NextOfKin,
  /**
   * notary public
   */
  NotaryPublic: v3_RoleClass_NotaryPublic,
  /**
   * An Entity (player) for which someone (scoper) is granted by law the right to call the material (player) his own.  This entitles the scoper to make decisions about the disposition of that material.
   */
  OwnedEntity: v3_RoleClass_OwnedEntity,
  /**
   * Definition:  an association between two Entities where the playing Entity (the part) is a component of the whole (scoper) in the sense of an integral structural component, that is distinct from other parts in the same whole, has a distinct function in the whole, and, as an effect, the full integrity of the whole depends (to some degree) on the presence of this part, even though the part may often be separable from the whole.
   *
   *
   *                            Discussion: Part is defined in opposition to (a) ingredient (not separable), (b) content (not a functional component), and (c) member (not functionally distinct from other members).
   */
  Part: v3_RoleClass_Part,
  /**
   * A Role of a LivingSubject (player) as an actual or potential recipient of health care services from a healthcare provider organization (scoper).
   *
   *
   *                            Usage Note: Communication about relationships between patients and specific healthcare practitioners (people) is not done via scoper.  Instead this is generally done using the CareProvision act.  This allows linkage between patient and a particular healthcare practitioner role and also allows description of the type of care involved in the relationship.
   */
  Patient: v3_RoleClass_Patient,
  /**
   * The role of an organization or individual designated to receive payment for a claim against a particular coverage. The scoping entity is the organization that is the submitter of the invoice in question.
   */
  Payee: v3_RoleClass_Payee,
  /**
   * The role of an organization that undertakes to accept claims invoices, assess the coverage or payments due for those invoices and pay to the designated payees for those invoices.  This role may be either the underwriter or a third-party organization authorized by the underwriter.  The scoping entity is the organization that underwrites the claimed coverage.
   */
  InvoicePayor: v3_RoleClass_InvoicePayor,
  /**
   * A role played by a person or organization that holds an insurance policy.  The underwriter of that policy is the scoping entity.
   *
   *
   *                            Discussion:The identifier of the policy is captured in 'Role.id' when the Role is a policy holder.
   *
   *                         A particular policy may cover several individuals one of whom may be, but need not be, the policy holder.  Thus the notion of covered party is a role that is distinct from that of the policy holder.
   */
  PolicyHolder: v3_RoleClass_PolicyHolder,
  /**
   * Description: A role played by a party that meets the eligibility criteria for coverage under a program.  A program eligible may be either a person, non-person living subject, or an organization, or a group of persons, non-person living subjects, or organizations.
   *
   *
   *                            Discussion: A program as typically government administered coverage for parties determined eligible under the terms of the program.
   *
   *
   *                            Note: The party playing a program eligible is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However a program eligible may make a claim under program, e.g., an unemployed worker may claim benefits under an unemployment insurance program, but parties playing these covered party role classes are not, for purposes of this vocabulary and in an effort to clearly distinguish role classes, considered claimants.
   *
   *                         In the case of a program eligible, a role type code INJWKR (injured worker) subtypes the class to indicate that the covered party in a workers compensation program is an injured worker, and as such, has filed a "claim" under the program for benefits.  Likewise, a covered role type code UNEMP (unemployed worker) subtypes the program eligible class to indicate that the covered party in an unemployment insurance program has filed a claim for unemployment benefits.
   *
   *
   *                            Example: A party meeting eligibility criteria related to health or financial status, e.g., in the U.S., persons meeting health, demographic, or financial criteria established by state and federal law are eligible for Medicaid.
   */
  ProgramEligible: v3_RoleClass_ProgramEligible,
  /**
   * An Entity (player) that is authorized to provide health care services by some authorizing agency (scoper).
   */
  HealthcareProvider: v3_RoleClass_HealthcareProvider,
  /**
   * Links two entities with classCode PSN (person) in a personal relationship. The character of the relationship must be defined by a PersonalRelationshipRoleType code. The player and scoper are determined by PersonalRelationshipRoleType code as well.
   */
  PersonalRelationship: v3_RoleClass_PersonalRelationship,
  /**
   * A substance (player) added to a mixture (scoper) to prevent microorganisms (fungi, bacteria) to spoil the mixture.
   */
  Preservative: v3_RoleClass_Preservative,
  /**
   * An entity (player) that has been recognized as having certain training/experience or other characteristics that would make said entity an appropriate performer for a certain activity. The scoper is an organization that educates or qualifies entities.
   */
  QualifiedEntity: v3_RoleClass_QualifiedEntity,
  /**
   * Definition:Specifies the administrative functionality within a formal experimental design for which the ResearchSubject role was established.
   *
   *
   *                            Examples: Screening - role is used for pre-enrollment evaluation portion of the design; enrolled - role is used for subjects admitted to the experimental portion of the design.
   */
  ResearchSubject: v3_RoleClass_ResearchSubject,
  /**
   * Material (player) sold by a retailer (scoper), who also give advice to prospective buyers.
   */
  RetailedMaterial: v3_RoleClass_RetailedMaterial,
  /**
   * A product regulated by some governmentatl orgnization.  The role is played by Material and scoped by Organization.
   *
   *                         Rationale: To support an entity clone used to identify the NDC number for a drug product.
   */
  RegulatedProduct: v3_RoleClass_RegulatedProduct,
  /**
   * Corresponds to the Role class
   */
  Role: v3_RoleClass_Role,
  /**
   * The "same" role asserts an identity between playing and scoping entities, i.e., that they are in fact two records of the same entity instance, and, in the case of discrepancies (e.g different DOB, gender), that one or both are in error.
   *
   *
   *                            Usage:
   *
   *
   *                         playing and scoping entities must have same classCode, but need not have identical attributes or values.
   *
   *
   *                            Example:
   *
   *
   *                         a provider registry maintains sets of conflicting demographic data for what is reported to be the same individual.
   */
  Same: v3_RoleClass_Same,
  /**
   * A role played by a place at which services may be provided.
   */
  ServiceDeliveryLocation: v3_RoleClass_ServiceDeliveryLocation,
  /**
   * The "same" roleclass asserts an identity between playing and scoping entities: that they are in fact instances of the same entity and, in the case of discrepancies (e.g different DOB, gender), that one or both are in error.
   *
   *
   *                            Usage:
   *
   *
   *                         playing and scoping entities must have same classcode, but need not have identical attributes or values.
   *
   *
   *                            Example:
   *
   *
   *                         a provider registry maintains sets of conflicting demographic data for what is reported to be the same individual.
   */
  Self: v3_RoleClass_Self,
  /**
   * The role of a person (player) who is the officer or signature authority for of a scoping entity, usually an organization (scoper).
   */
  SigningAuthorityOrOfficer: v3_RoleClass_SigningAuthorityOrOfficer,
  /**
   * A role played by a material entity that is a specimen for an act. It is scoped by the source of the specimen.
   */
  Specimen: v3_RoleClass_Specimen,
  /**
   * A role played by an entity, usually an organization that is the sponsor of an insurance plan or a health program. A sponsor is the party that is ultimately accountable for the coverage by employment contract or by law.  A sponsor can be an employer, union, government agency, or association.  Fully insured sponsors establish the terms of the plan and contract with health insurance plans to assume the risk and to administer the plan.  Self-insured sponsors delegate coverage administration, but not risk, to third-party administrators.  Program sponsors designate services to be covered in accordance with statute.   Program sponsors may administer the coverage themselves, delegate coverage administration, but not risk to third-party administrators, or contract with health insurance plans to assume the risk and administrator a program. Sponsors qualify individuals who may become
   *
   *
   *
   *                               a policy holder of the plan;
   *
   *
   *
   *                               where the sponsor is the policy holder, who may become a subscriber or a dependent to a policy under the plan; or
   *
   *
   *
   *                               where the sponsor is a government agency, who may become program eligibles under a program.
   *
   *
   *
   *                         The sponsor role may be further qualified by the SponsorRole.code.  Entities playing the sponsor role may also play the role of a Coverage Administrator.
   *
   *
   *                            Example: An employer, union, government agency, or association.
   */
  CoverageSponsor: v3_RoleClass_CoverageSponsor,
  /**
   * A stabilizer (player) added to a mixture (scoper) in order to prevent the molecular disintegration of the main substance.
   */
  Stabilizer: v3_RoleClass_Stabilizer,
  /**
   * A role played by an individual who is a student of a school, which is the scoping entity.
   */
  Student: v3_RoleClass_Student,
  /**
   * Relates an entity (player) (e.g. a device) to a location (scoper) at which it is normally found or stored when not used.
   */
  StoredEntity: v3_RoleClass_StoredEntity,
  /**
   * An entity that subsumes the identity of another.  Used in the context of merging documented entity instances. Both the player and scoper must have the same classCode.
   *
   *                         The use of this code is deprecated in favor of the term SUBY which is its inverse and is more ontologically correct.
   */
  Subsumer: v3_RoleClass_Subsumer,
  /**
   * Description: A role played by a person covered under a policy based on association with a sponsor who is the policy holder, and whose association may provide for the eligibility of dependents for coverage.
   *
   *
   *                            Discussion: The policy holder holds the contract with the policy or program underwriter.  The subscriber holds a certificate of coverage under the contract.  In legal proceedings concerning the policy or program, the terms of the contract takes precedence over the terms of the certificate of coverage if there are any inconsistencies.
   *
   *
   *                            Note: The party playing the role of a subscriber is not a claimant in the sense conveyed by the RoleClassCoveredParty CLAIM (claimant).  However, a subscriber may make a claim under a policy, e.g., a subscriber under a health insurance policy may become the claimant for coverage under that policy for wellness examines or if injured and there is no liable third party.  In the case of a subscriber making a claim, a role type code INSCLM (insured claimant) subtypes the class to indicate that the subscriber has filed a claim for services covered under the health insurance policy.
   *
   *
   *                            Example: An employee or a member of an association.
   */
  Subscriber: v3_RoleClass_Subscriber,
  /**
   * Relates a prevailing record of an Entity (scoper) with another record (player) that it subsumes.
   *
   *
   *                            Examples: Show a correct new Person object (scoper) that subsumes one or more duplicate Person objects that had accidentally been created for the same physical person.
   *
   *
   *                            Constraints: Both the player and scoper must have the same classCode.
   */
  SubsumedBy: v3_RoleClass_SubsumedBy,
  /**
   * Relates a place entity (player) as the region over which the scoper (typically an Organization) has certain authority (jurisdiction). For example, the Calgary Regional Health Authority (scoper) has authority over the territory "Region 4 of Alberta" (player) in matters of health.
   */
  TerritoryOfAuthority: v3_RoleClass_TerritoryOfAuthority,
  /**
   * A manufactured material (player) that is used for its therapeutic properties.  The manufacturer is the scoper.
   */
  TherapeuticAgent: v3_RoleClass_TherapeuticAgent,
  /**
   * A role played by a person or an organization.  It is the party that
   *
   *
   *
   *                               accepts fiscal responsibility for insurance plans and the policies created under those plans;
   *
   *
   *
   *                               administers and accepts fiscal responsibility for a program that provides coverage for services to eligible individuals; and/or
   *
   *
   *
   *                               has the responsibility to assess the merits of each risk and decide a suitable premium for accepting all or part of the risk.  If played by an organization, this role may be further specified by an appropriate RoleCode.
   *
   *
   *
   *
   *                            Example:
   *
   *
   *
   *
   *                               A health insurer;
   *
   *
   *
   *                               Medicaid Program;
   *
   *
   *
   *                               Lloyd's of London
   */
  Underwriter: v3_RoleClass_Underwriter,
  /**
   * Description:An entity (player) that is used by another entity (scoper)
   */
  UsedEntity: v3_RoleClass_UsedEntity,
  /**
   * A role a product plays when a guarantee is given to the purchaser by the seller (scoping entity) stating that the product is reliable and free from known defects and that the seller will repair or replace defective parts within a given time limit and under certain conditions.
   */
  WarrantedProduct: v3_RoleClass_WarrantedProduct,
};
/**
 *  A role played by a place at which services may be provided.
 */
export const V3RoleClassServiceDeliveryLocation = {
  /**
   * A role of a place (player) that is intended to house the provision of services. Scoper is the Entity (typically Organization) that provides these services. This is not synonymous with "ownership."
   */
  DedicatedServiceDeliveryLocation: v3_RoleClass_DedicatedServiceDeliveryLocation,
  /**
   * A role played by a place at which health care services may be provided without prior designation or authorization.
   */
  IncidentalServiceDeliveryLocation: v3_RoleClass_IncidentalServiceDeliveryLocation,
  /**
   * A role played by a place at which services may be provided.
   */
  ServiceDeliveryLocation: v3_RoleClass_ServiceDeliveryLocation,
};
/**
 *  A role played by a material entity that is a specimen for an act. It is scoped by the source of the specimen.
 */
export const V3RoleClassSpecimen = {
  /**
   * A portion (player) of an original or source specimen (scoper) used for testing or transportation.
   */
  Aliquot: v3_RoleClass_Aliquot,
  /**
   * A microorganism that has been isolated from other microorganisms or a source matrix.
   */
  Isolate: v3_RoleClass_Isolate,
  /**
   * A role played by a material entity that is a specimen for an act. It is scoped by the source of the specimen.
   */
  Specimen: v3_RoleClass_Specimen,
};
const v3_RoleCode_AdministrativeContactRoleType: Coding = {
  code: '_AdministrativeContactRoleType',
  display: 'AdministrativeContactRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AffiliationRoleType: Coding = {
  code: '_AffiliationRoleType',
  display: 'AffiliationRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AgentRoleType: Coding = {
  code: '_AgentRoleType',
  display: 'AgentRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AssignedNonPersonLivingSubjectRoleType: Coding = {
  code: '_AssignedNonPersonLivingSubjectRoleType',
  display: 'AssignedNonPersonLivingSubjectRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AssignedRoleType: Coding = {
  code: '_AssignedRoleType',
  display: 'AssignedRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_CertifiedEntityType: Coding = {
  code: '_CertifiedEntityType',
  display: 'CertifiedEntityType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ClaimantCoveredPartyRoleType: Coding = {
  code: '_ClaimantCoveredPartyRoleType',
  display: 'ClaimantCoveredPartyRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ContactRoleType: Coding = {
  code: '_ContactRoleType',
  display: 'ContactRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_CoverageRoleType: Coding = {
  code: '_CoverageRoleType',
  display: 'CoverageRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_CoverageSponsorRoleType: Coding = {
  code: '_CoverageSponsorRoleType',
  display: 'CoverageSponsorRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_CoveredPartyRoleType: Coding = {
  code: '_CoveredPartyRoleType',
  display: 'covered party role type',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_DedicatedClinicalLocationRoleType: Coding = {
  code: '_DedicatedClinicalLocationRoleType',
  display: 'DedicatedClinicalLocationRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_DedicatedNonClinicalLocationRoleType: Coding = {
  code: '_DedicatedNonClinicalLocationRoleType',
  display: 'DedicatedNonClinicalLocationRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_DedicatedServiceDeliveryLocationRoleType: Coding = {
  code: '_DedicatedServiceDeliveryLocationRoleType',
  display: 'DedicatedServiceDeliveryLocationRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_DependentCoveredPartyRoleType: Coding = {
  code: '_DependentCoveredPartyRoleType',
  display: 'DependentCoveredPartyRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_IdentifiedEntityType: Coding = {
  code: '_IdentifiedEntityType',
  display: 'IdentifiedEntityType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_IncidentalServiceDeliveryLocationRoleType: Coding = {
  code: '_IncidentalServiceDeliveryLocationRoleType',
  display: 'IncidentalServiceDeliveryLocationRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_IndividualInsuredPartyRoleType: Coding = {
  code: '_IndividualInsuredPartyRoleType',
  display: 'IndividualInsuredPartyRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_LivingSubjectProductionClass: Coding = {
  code: '_LivingSubjectProductionClass',
  display: 'LivingSubjectProductionClass',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_LocationIdentifiedEntityRoleCode: Coding = {
  code: '_LocationIdentifiedEntityRoleCode',
  display: 'LocationIdentifiedEntityRoleCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MedicationGeneralizationRoleType: Coding = {
  code: '_MedicationGeneralizationRoleType',
  display: 'MedicationGeneralizationRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MemberRoleType: Coding = {
  code: '_MemberRoleType',
  display: 'MemberRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PayorRoleType: Coding = {
  code: '_PayorRoleType',
  display: 'PayorRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PolicyOrProgramCoverageRoleType: Coding = {
  code: '_PolicyOrProgramCoverageRoleType',
  display: 'PolicyOrProgramCoverageRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ProgramEligiblePartyRoleType: Coding = {
  code: '_ProgramEligiblePartyRoleType',
  display: 'ProgramEligiblePartyRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ResearchSubjectRoleBasis: Coding = {
  code: '_ResearchSubjectRoleBasis',
  display: 'ResearchSubjectRoleBasis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ServiceDeliveryLocationRoleType: Coding = {
  code: '_ServiceDeliveryLocationRoleType',
  display: 'ServiceDeliveryLocationRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SpecimenRoleType: Coding = {
  code: '_SpecimenRoleType',
  display: 'SpecimenRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SubscriberCoveredPartyRoleType: Coding = {
  code: '_SubscriberCoveredPartyRoleType',
  display: 'SubscriberCoveredPartyRoleType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AccidentSite: Coding = {
  code: 'ACC',
  display: 'accident site',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AccreditationLocationIdentifier: Coding = {
  code: 'ACHFID',
  display: 'accreditation location identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AdoptedChild_2: Coding = {
  code: 'ADOPT',
  display: 'adopted child',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AllergyClinic: Coding = {
  code: 'ALL',
  display: 'Allergy clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Ambulance: Coding = {
  code: 'AMB',
  display: 'Ambulance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Amender: Coding = {
  code: 'AMENDER',
  display: 'amender',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AmputeeClinic: Coding = {
  code: 'AMPUT',
  display: 'Amputee clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Antibiotic: Coding = {
  code: 'ANTIBIOT',
  display: 'Antibiotic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AssistiveNonPersonLivingSubject: Coding = {
  code: 'ASSIST',
  display: 'Assistive non-person living subject',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Blind: Coding = {
  code: 'B',
  display: 'Blind',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Beef: Coding = {
  code: 'BF',
  display: 'Beef',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_BillingContact: Coding = {
  code: 'BILL',
  display: 'Billing Contact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_BiotherapeuticNonPersonLivingSubject: Coding = {
  code: 'BIOTH',
  display: 'Biotherapeutic non-person living subject',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Broiler: Coding = {
  code: 'BL',
  display: 'Broiler',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_BoneMarrowTransplantClinic: Coding = {
  code: 'BMTC',
  display: 'Bone marrow transplant clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_BoneMarrowTransplantUnit: Coding = {
  code: 'BMTU',
  display: 'Bone marrow transplant unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Breeder: Coding = {
  code: 'BR',
  display: 'Breeder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_BreastClinic: Coding = {
  code: 'BREAST',
  display: 'Breast clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Calibrator: Coding = {
  code: 'C',
  display: 'Calibrator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ChildAndAdolescentNeurologyClinic: Coding = {
  code: 'CANC',
  display: 'Child and adolescent neurology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ChildAndAdolescentPsychiatryClinic: Coding = {
  code: 'CAPC',
  display: 'Child and adolescent psychiatry clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AmbulatoryHealthCareFacilitiesClinicCenterRehabilitationCardiacFacilities: Coding = {
  code: 'CARD',
  display: 'Ambulatory Health Care Facilities; Clinic/Center; Rehabilitation: Cardiac Facilities',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_CardiacCatheterizationLab: Coding = {
  code: 'CATH',
  display: 'Cardiac catheterization lab',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ClinicalCompanion: Coding = {
  code: 'CCO',
  display: 'Clinical Companion',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_CoronaryCareUnit: Coding = {
  code: 'CCU',
  display: 'Coronary care unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ChestUnit: Coding = {
  code: 'CHEST',
  display: 'Chest unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ChronicCareFacility: Coding = {
  code: 'CHR',
  display: 'Chronic Care Facility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Claimant: Coding = {
  code: 'CLAIM',
  display: 'claimant',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Companion: Coding = {
  code: 'CO',
  display: 'Companion',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_CoagulationClinic: Coding = {
  code: 'COAG',
  display: 'Coagulation clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ContinuityOfCoverageBeneficiary: Coding = {
  code: 'COCBEN',
  display: 'continuity of coverage beneficiary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_CommunityLocation: Coding = {
  code: 'COMM',
  display: 'Community Location',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_CommunityLaboratory: Coding = {
  code: 'communityLaboratory',
  display: 'Community Laboratory',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_CrimeVictim: Coding = {
  code: 'CRIMEVIC',
  display: 'crime victim',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ColonAndRectalSurgeryClinic: Coding = {
  code: 'CRS',
  display: 'Colon and rectal surgery clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_CommunityServiceCenter: Coding = {
  code: 'CSC',
  display: 'community service center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_CardiovascularDiagnosticsOrTherapeuticsUnit: Coding = {
  code: 'CVDX',
  display: 'Cardiovascular diagnostics or therapeutics unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Dairy: Coding = {
  code: 'DA',
  display: 'Dairy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_DeliveryAddress: Coding = {
  code: 'DADDR',
  display: 'Delivery Address',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_TherapeuticClass: Coding = {
  code: 'DC',
  display: 'therapeutic class',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Debridement: Coding = {
  code: 'DEBR',
  display: 'Debridement',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_DEP: Coding = {
  code: 'DEP',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Dependent: Coding = {
  code: 'DEPEN',
  display: 'dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_DermatologyClinic: Coding = {
  code: 'DERM',
  display: 'Dermatology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_DifferentlyAbled: Coding = {
  code: 'DIFFABL',
  display: 'differently abled',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Draft: Coding = {
  code: 'DR',
  display: 'Draft',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_DerivativeClassifier: Coding = {
  code: 'DRIVCLASSIFIER',
  display: 'derivative classifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Dual: Coding = {
  code: 'DU',
  display: 'Dual',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_DiagnosticsOrTherapeuticsUnit: Coding = {
  code: 'DX',
  display: 'Diagnostics or therapeutics unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ElectronicQC: Coding = {
  code: 'E',
  display: 'Electronic QC',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_EchocardiographyLab: Coding = {
  code: 'ECHO',
  display: 'Echocardiography lab',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_EndocrinologyClinic: Coding = {
  code: 'ENDO',
  display: 'Endocrinology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_EndoscopyLab: Coding = {
  code: 'ENDOS',
  display: 'Endoscopy lab',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_EnrollmentBroker: Coding = {
  code: 'ENROLBKR',
  display: 'Enrollment Broker',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_OtorhinolaryngologyClinic: Coding = {
  code: 'ENT',
  display: 'Otorhinolaryngology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_EpilepsyUnit: Coding = {
  code: 'EPIL',
  display: 'Epilepsy unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_EmergencyRoom: Coding = {
  code: 'ER',
  display: 'Emergency room',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Enrollment: Coding = {
  code: 'ERL',
  display: 'enrollment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_EmergencyTraumaUnit: Coding = {
  code: 'ETU',
  display: 'Emergency trauma unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FillerProficiency: Coding = {
  code: 'F',
  display: 'Filler Proficiency',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FamilyDependent: Coding = {
  code: 'FAMDEP',
  display: 'family dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Fiber: Coding = {
  code: 'FI',
  display: 'Fiber',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FamilyMember_2: Coding = {
  code: 'FM',
  display: 'Family Member',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FamilyMedicineClinic: Coding = {
  code: 'FMC',
  display: 'Family medicine clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FullTimeStudent: Coding = {
  code: 'FSTUD',
  display: 'full-time student',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_FullyInsuredCoverageSponsor: Coding = {
  code: 'FULLINS',
  display: 'Fully insured coverage sponsor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Group: Coding = {
  code: 'G',
  display: 'Group',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_HospitalsGeneralAcuteCareHospital: Coding = {
  code: 'GACH',
  display: 'Hospitals; General Acute Care Hospital',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Grandchild_2: Coding = {
  code: 'GCHILD',
  display: 'grandchild',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_GenericDrug: Coding = {
  code: 'GD',
  display: 'generic drug',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_GenericDrugForm: Coding = {
  code: 'GDF',
  display: 'generic drug form',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_GenericDrugStrength: Coding = {
  code: 'GDS',
  display: 'generic drug strength',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_GenericDrugStrengthForm: Coding = {
  code: 'GDSF',
  display: 'generic drug strength form',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_GastroenterologyClinic: Coding = {
  code: 'GI',
  display: 'Gastroenterology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_GastroenterologyDiagnosticsOrTherapeuticsLab: Coding = {
  code: 'GIDX',
  display: 'Gastroenterology diagnostics or therapeutics lab',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_GeneralInternalMedicineClinic: Coding = {
  code: 'GIM',
  display: 'General internal medicine clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Grandparent_2: Coding = {
  code: 'GPARNT',
  display: 'grandparent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Guardian: Coding = {
  code: 'GUARD',
  display: 'guardian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_GynecologyClinic: Coding = {
  code: 'GYN',
  display: 'Gynecology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_HandClinic: Coding = {
  code: 'HAND',
  display: 'Hand clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_HandicappedDependent: Coding = {
  code: 'HANDIC',
  display: 'handicapped dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_HemodialysisUnit: Coding = {
  code: 'HD',
  display: 'Hemodialysis unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_HematologyClinic: Coding = {
  code: 'HEM',
  display: 'Hematology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_HospitalLaboratory: Coding = {
  code: 'HLAB',
  display: 'hospital laboratory',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_HomeHealth: Coding = {
  code: 'homeHealth',
  display: 'Home Health',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Hospital: Coding = {
  code: 'HOSP',
  display: 'Hospital',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_RadiologyUnit: Coding = {
  code: 'HRAD',
  display: 'radiology unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_HypertensionClinic: Coding = {
  code: 'HTN',
  display: 'Hypertension clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_HospitalUnit: Coding = {
  code: 'HU',
  display: 'Hospital unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SpecimenCollectionSite: Coding = {
  code: 'HUSCS',
  display: 'specimen collection site',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_IntensiveCareUnit: Coding = {
  code: 'ICU',
  display: 'Intensive care unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ImpairmentEvaluationCenter: Coding = {
  code: 'IEC',
  display: 'Impairment evaluation center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MemberOfAnIndigenousPeople: Coding = {
  code: 'INDIG',
  display: 'member of an indigenous people',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Individual: Coding = {
  code: 'INDIV',
  display: 'individual',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_InfectiousDiseaseClinic: Coding = {
  code: 'INFD',
  display: 'Infectious disease clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_InjuredPlaintiff: Coding = {
  code: 'INJ',
  display: 'injured plaintiff',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_InjuredWorker: Coding = {
  code: 'INJWKR',
  display: 'injured worker',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_InpatientLaboratory: Coding = {
  code: 'INLAB',
  display: 'inpatient laboratory',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_InpatientPharmacy: Coding = {
  code: 'INPHARM',
  display: 'inpatient pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_InfertilityClinic: Coding = {
  code: 'INV',
  display: 'Infertility clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_JurisdictionLocationIdentifier: Coding = {
  code: 'JURID',
  display: 'jurisdiction location identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Pool: Coding = {
  code: 'L',
  display: 'Pool',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Laboratory: Coding = {
  code: 'laboratory',
  display: 'Laboratory',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_LocalLocationIdentifier: Coding = {
  code: 'LOCHFID',
  display: 'local location identifier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Layer: Coding = {
  code: 'LY',
  display: 'Layer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_LympedemaClinic: Coding = {
  code: 'LYMPH',
  display: 'Lympedema clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MedicalLaboratory: Coding = {
  code: 'MBL',
  display: 'medical laboratory',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ManufacturedDrugStrengthForm: Coding = {
  code: 'MGDSF',
  display: 'manufactured drug strength form',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MedicalGeneticsClinic: Coding = {
  code: 'MGEN',
  display: 'Medical genetics clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MilitaryHospital: Coding = {
  code: 'MHSP',
  display: 'Military Hospital',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MobileUnit: Coding = {
  code: 'MOBL',
  display: 'Mobile Unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Meat: Coding = {
  code: 'MT',
  display: 'Meat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Multiplier: Coding = {
  code: 'MU',
  display: 'Multiplier',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NamedInsured: Coding = {
  code: 'NAMED',
  display: 'named insured',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NaturalChild_2: Coding = {
  code: 'NAT',
  display: 'natural child',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NursingOrCustodialCareFacility: Coding = {
  code: 'NCCF',
  display: 'Nursing or custodial care facility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NeurologyCriticalCareAndStrokeUnit: Coding = {
  code: 'NCCS',
  display: 'Neurology critical care and stroke unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NephrologyClinic: Coding = {
  code: 'NEPH',
  display: 'Nephrology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NeurologyClinic: Coding = {
  code: 'NEUR',
  display: 'Neurology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NieceNephew_2: Coding = {
  code: 'NIENE',
  display: 'niece/nephew',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NeurosurgeryUnit: Coding = {
  code: 'NS',
  display: 'Neurosurgery unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_OperatorProficiency: Coding = {
  code: 'O',
  display: 'Operator Proficiency',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ObstetricsClinic: Coding = {
  code: 'OB',
  display: 'Obstetrics clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_OutpatientFacility: Coding = {
  code: 'OF',
  display: 'Outpatient facility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_OralAndMaxillofacialSurgeryClinic: Coding = {
  code: 'OMS',
  display: 'Oral and maxillofacial surgery clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_MedicalOncologyClinic: Coding = {
  code: 'ONCL',
  display: 'Medical oncology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_OpthalmologyClinic: Coding = {
  code: 'OPH',
  display: 'Opthalmology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_OptometryClinic: Coding = {
  code: 'OPTC',
  display: 'optometry clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_OrganizationalContact: Coding = {
  code: 'ORG',
  display: 'organizational contact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_OrthopedicsClinic: Coding = {
  code: 'ORTHO',
  display: 'Orthopedics clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_OutpatientLaboratory: Coding = {
  code: 'OUTLAB',
  display: 'outpatient laboratory',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_OutpatientPharmacy: Coding = {
  code: 'OUTPHARM',
  display: 'outpatient pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Patient: Coding = {
  code: 'P',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PainClinic: Coding = {
  code: 'PAINCL',
  display: 'Pain clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Parent_2: Coding = {
  code: 'PARNT',
  display: 'parent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Pathologist: Coding = {
  code: 'pathologist',
  display: 'Pathologist',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PayorContact: Coding = {
  code: 'PAYOR',
  display: 'Payor Contact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PrimaryCareClinic: Coding = {
  code: 'PC',
  display: 'Primary care clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PediatricsClinic: Coding = {
  code: 'PEDC',
  display: 'Pediatrics clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PediatricCardiologyClinic: Coding = {
  code: 'PEDCARD',
  display: 'Pediatric cardiology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PediatricEndocrinologyClinic: Coding = {
  code: 'PEDE',
  display: 'Pediatric endocrinology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PediatricGastroenterologyClinic: Coding = {
  code: 'PEDGI',
  display: 'Pediatric gastroenterology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PediatricHematologyClinic: Coding = {
  code: 'PEDHEM',
  display: 'Pediatric hematology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PediatricOncologyClinic: Coding = {
  code: 'PEDHO',
  display: 'Pediatric oncology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PediatricIntensiveCareUnit: Coding = {
  code: 'PEDICU',
  display: 'Pediatric intensive care unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PediatricInfectiousDiseaseClinic: Coding = {
  code: 'PEDID',
  display: 'Pediatric infectious disease clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PediatricNephrologyClinic: Coding = {
  code: 'PEDNEPH',
  display: 'Pediatric nephrology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PediatricNeonatalIntensiveCareUnit: Coding = {
  code: 'PEDNICU',
  display: 'Pediatric neonatal intensive care unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PediatricRheumatologyClinic: Coding = {
  code: 'PEDRHEUM',
  display: 'Pediatric rheumatology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PediatricUnit: Coding = {
  code: 'PEDU',
  display: 'Pediatric unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PolicyHolder: Coding = {
  code: 'PH',
  display: 'Policy Holder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Pharmacy: Coding = {
  code: 'PHARM',
  display: 'Pharmacy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Phlebotomist: Coding = {
  code: 'phlebotomist',
  display: 'Phlebotomist',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PsychiatricHospitalUnit: Coding = {
  code: 'PHU',
  display: 'Psychiatric hospital unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Pleasure: Coding = {
  code: 'PL',
  display: 'Pleasure',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PlasticSurgeryClinic: Coding = {
  code: 'PLS',
  display: 'Plastic surgery clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PodiatryClinic: Coding = {
  code: 'POD',
  display: 'Podiatry clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PainRehabilitationCenter: Coding = {
  code: 'PRC',
  display: 'Pain rehabilitation center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PreventiveMedicineClinic: Coding = {
  code: 'PREV',
  display: 'Preventive medicine clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ProctologyClinic: Coding = {
  code: 'PROCTO',
  display: 'Proctology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ProviderQuoteSOffice: Coding = {
  code: 'PROFF',
  display: "Provider's Office",
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ProgramEligible: Coding = {
  code: 'PROG',
  display: 'program eligible',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ProsthodonticsClinic: Coding = {
  code: 'PROS',
  display: 'Prosthodontics clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PsychologyClinic: Coding = {
  code: 'PSI',
  display: 'Psychology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PartTimeStudent: Coding = {
  code: 'PSTUD',
  display: 'part-time student',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PsychiatryClinic: Coding = {
  code: 'PSY',
  display: 'Psychiatry clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PSYCHCF: Coding = {
  code: 'PSYCHCF',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PsychatricCareFacility: Coding = {
  code: 'PSYCHF',
  display: 'Psychatric Care Facility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Patient_2: Coding = {
  code: 'PT',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PatientQuoteSResidence: Coding = {
  code: 'PTRES',
  display: "Patient's Residence",
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_QualityControl: Coding = {
  code: 'Q',
  display: 'Quality Control',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Replicate: Coding = {
  code: 'R',
  display: 'Replicate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_RadiologyDiagnosticsOrTherapeuticsUnit: Coding = {
  code: 'RADDX',
  display: 'Radiology diagnostics or therapeutics unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_RadiationOncologyUnit: Coding = {
  code: 'RADO',
  display: 'Radiation oncology unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Racing: Coding = {
  code: 'RC',
  display: 'Racing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Retiree: Coding = {
  code: 'RETIREE',
  display: 'retiree',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Reviewer: Coding = {
  code: 'REVIEWER',
  display: 'reviewer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_RehabilitationHospital: Coding = {
  code: 'RH',
  display: 'Rehabilitation hospital',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_AddictionTreatmentCenter: Coding = {
  code: 'RHAT',
  display: 'addiction treatment center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_RheumatologyClinic: Coding = {
  code: 'RHEUM',
  display: 'Rheumatology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_IntellectualImpairmentCenter: Coding = {
  code: 'RHII',
  display: 'intellectual impairment center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ParentsWithAdjustmentDifficultiesCenter: Coding = {
  code: 'RHMAD',
  display: 'parents with adjustment difficulties center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PhysicalImpairmentCenter: Coding = {
  code: 'RHPI',
  display: 'physical impairment center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PhysicalImpairmentHearingCenter: Coding = {
  code: 'RHPIH',
  display: 'physical impairment - hearing center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PhysicalImpairmentMotorSkillsCenter: Coding = {
  code: 'RHPIMS',
  display: 'physical impairment - motor skills center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_PhysicalImpairmentVisualSkillsCenter: Coding = {
  code: 'RHPIVS',
  display: 'physical impairment - visual skills center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_RehabilitationHospitalUnit: Coding = {
  code: 'RHU',
  display: 'Rehabilitation hospital unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_YouthsWithAdjustmentDifficultiesCenter: Coding = {
  code: 'RHYAD',
  display: 'youths with adjustment difficulties center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_NeuroradiologyUnit: Coding = {
  code: 'RNEU',
  display: 'Neuroradiology unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ResidentialTreatmentFacility: Coding = {
  code: 'RTF',
  display: 'Residential treatment facility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_School: Coding = {
  code: 'SCHOOL',
  display: 'school',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Screening: Coding = {
  code: 'SCN',
  display: 'screening',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Seeing: Coding = {
  code: 'SEE',
  display: 'Seeing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Self_2: Coding = {
  code: 'SELF',
  display: 'self',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SelfInsuredCoverageSponsor: Coding = {
  code: 'SELFINS',
  display: 'Self insured coverage sponsor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Show: Coding = {
  code: 'SH',
  display: 'Show',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SleepDisordersUnit: Coding = {
  code: 'SLEEP',
  display: 'Sleep disorders unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SkilledNursingFacility: Coding = {
  code: 'SNF',
  display: 'Skilled nursing facility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Sniffing: Coding = {
  code: 'SNIFF',
  display: 'Sniffing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SportsMedicineClinic: Coding = {
  code: 'SPMED',
  display: 'Sports medicine clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SponsoredDependent: Coding = {
  code: 'SPON',
  display: 'sponsored dependent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Spouse_2: Coding = {
  code: 'SPSE',
  display: 'spouse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_StepChild_2: Coding = {
  code: 'STEP',
  display: 'step child',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Student: Coding = {
  code: 'STUD',
  display: 'student',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SurgeryClinic: Coding = {
  code: 'SU',
  display: 'Surgery clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Self_3: Coding = {
  code: 'subject',
  display: 'Self',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Subscriber: Coding = {
  code: 'SUBSCR',
  display: 'subscriber',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_SubstanceUseRehabilitationFacility: Coding = {
  code: 'SURF',
  display: 'Substance use rehabilitation facility',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ThirdParty: Coding = {
  code: 'thirdParty',
  display: 'Third Party',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_ThirdPartyAdministrator: Coding = {
  code: 'TPA',
  display: 'Third party administrator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_TransplantClinic: Coding = {
  code: 'TR',
  display: 'Transplant clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_TravelAndGeographicMedicineClinic: Coding = {
  code: 'TRAVEL',
  display: 'Travel and geographic medicine clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_TribalMember: Coding = {
  code: 'TRB',
  display: 'Tribal Member',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_UtilizationManagementOrganization: Coding = {
  code: 'UMO',
  display: 'Utilization management organization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_UnderageProtectionCenter: Coding = {
  code: 'UPC',
  display: 'underage protection center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_UrologyClinic: Coding = {
  code: 'URO',
  display: 'Urology clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Verifying: Coding = {
  code: 'V',
  display: 'Verifying',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Validator: Coding = {
  code: 'VALIDATOR',
  display: 'validator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Veal: Coding = {
  code: 'VL',
  display: 'Veal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Ward: Coding = {
  code: 'WARD',
  display: 'ward',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Wool: Coding = {
  code: 'WL',
  display: 'Wool',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_WoundClinic: Coding = {
  code: 'WND',
  display: 'Wound clinic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_Working: Coding = {
  code: 'WO',
  display: 'Working',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
const v3_RoleCode_WorkSite: Coding = {
  code: 'WORK',
  display: 'work site',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
/**
 *  A set of codes further specifying the kind of Role; specific classification codes for further qualifying RoleClass codes.
 */
export const V3RoleCode = {
  /**
   * A contact role used for business and/or administrative purposes.
   */
  AdministrativeContactRoleType: v3_RoleCode_AdministrativeContactRoleType,
  /**
   * Concepts characterizing the type of association formed by player and scoper when there is a recognized Affiliate role by which the two parties are related.
   *
   *
   *                            Examples: Business Partner, Business Associate, Colleague
   */
  AffiliationRoleType: v3_RoleCode_AffiliationRoleType,
  /**
   * Parties that may or should contribute or have contributed to an Act.
   */
  AgentRoleType: v3_RoleCode_AgentRoleType,
  /**
   * Description:A role type that is used to further qualify a non-person subject playing a role where the role class attribute is set to RoleClass AssignedEntity
   */
  AssignedNonPersonLivingSubjectRoleType: v3_RoleCode_AssignedNonPersonLivingSubjectRoleType,
  /**
   * AssignedRoleType
   */
  AssignedRoleType: v3_RoleCode_AssignedRoleType,
  /**
   * Defines types of certifications for all entities
   */
  CertifiedEntityType: v3_RoleCode_CertifiedEntityType,
  /**
   * A role type used to qualify a person's legal status within a country or nation.
   */
  CitizenRoleType: v3_RoleCode_CitizenRoleType,
  /**
   * DescriptionA role recognized through the eligibility of a party play a claimant for benefits covered or provided under an insurance policy.
   */
  ClaimantCoveredPartyRoleType: v3_RoleCode_ClaimantCoveredPartyRoleType,
  /**
   * Types of contact for Role code "CON"
   */
  ContactRoleType: v3_RoleCode_ContactRoleType,
  /**
   * Role recognized through the issuance of insurance coverage to an identified covered party who has this relationship with the policy holder such as the policy holder themselves (self), spouse, child, etc
   */
  CoverageRoleType: v3_RoleCode_CoverageRoleType,
  /**
   * Description:Codes that indicate a specific type of sponsor.  Used when the sponsor's role is only either as a fully insured sponsor or only as a self-insured sponsor.  NOTE: Where a sponsor may be either, use the SponsorParticipationFunction.code (fully insured or self insured) to indicate the type of responsibility. (CO6-0057)
   */
  CoverageSponsorRoleType: v3_RoleCode_CoverageSponsorRoleType,
  /**
   * A role recognized through the eligibility of an identified living subject for benefits covered under an insurance policy or a program.  Eligibility as a covered party may be conditioned on a relationship with (1) the policy holder such as the policy holder who is covered as an individual under a poliy or as a party sponsored for coverage by the policy holder.
   *
   *
   *                            Example:An employee as a subscriber; or (2) on being scoped another covered party such as the subscriber, as in the case of a dependent.
   *
   *
   *                            Discussion:  The Abstract Value Set "CoverageRoleType", which was developed for use in the Canadian realm "pre-coordinate" coverage roles with other roles that a covered party must play in order to be eligible for coverage, e.g., "handicapped dependent".  Other codes in the Abstract Value Set CoveredPartyRoleType domain can be "post-coordinated" with the EligiblePartyRoleType codes to denote comparable concepts.  Decoupling the concepts is intended to support a wider range of concepts and semantic comparability of coded concepts.
   */
  CoveredPartyRoleType: v3_RoleCode_CoveredPartyRoleType,
  /**
   * A role of a place that further classifies the clinical setting (e.g., cardiology clinic, primary care clinic, rehabilitation hospital, skilled nursing facility) in which care is delivered during an encounter.
   */
  DedicatedClinicalLocationRoleType: v3_RoleCode_DedicatedClinicalLocationRoleType,
  /**
   * A role of a place that further classifies a setting that is intended to house the provision of non-clinical services.
   */
  DedicatedNonClinicalLocationRoleType: v3_RoleCode_DedicatedNonClinicalLocationRoleType,
  /**
   * A role of a place that further classifies a setting that is intended to house the provision of services.
   */
  DedicatedServiceDeliveryLocationRoleType: v3_RoleCode_DedicatedServiceDeliveryLocationRoleType,
  /**
   * Description: A role recognized through the eligibility of a party to play a dependent for benefits covered or provided under a health insurance policy because of an association with the subscriber that is recognized by the policy underwriter.
   */
  DependentCoveredPartyRoleType: v3_RoleCode_DependentCoveredPartyRoleType,
  /**
   * Definition: A code representing the type of identifier that has been assigned to the identified entity (IDENT).
   *
   *
   *                            Examples: Example values include Social Insurance Number, Product Catalog ID, Product Model Number.
   */
  IdentifiedEntityType: v3_RoleCode_IdentifiedEntityType,
  /**
   * IncidentalServiceDeliveryLocationRoleType
   */
  IncidentalServiceDeliveryLocationRoleType: v3_RoleCode_IncidentalServiceDeliveryLocationRoleType,
  /**
   * A role recognized through the eligibility of a party to play an individual insured for benefits covered or provided under an insurance policy where the party is also the policy holder.
   */
  IndividualInsuredPartyRoleType: v3_RoleCode_IndividualInsuredPartyRoleType,
  /**
   * Code indicating the primary use for which a living subject is bred or grown
   */
  LivingSubjectProductionClass: v3_RoleCode_LivingSubjectProductionClass,
  /**
   * Description:Describes types of identifiers other than the primary location registry identifier for a service delivery location.  Identifiers may be assigned by a local service delivery organization, a formal body capable of accrediting the location for the capability to provide specific services or the identifier may be assigned at a jurisdictional level.
   */
  LocationIdentifiedEntityRoleCode: v3_RoleCode_LocationIdentifiedEntityRoleCode,
  /**
   * Identifies the specific hierarchical relationship between the playing and scoping medications.
   *
   *
   *                            Examples: Generic, Generic Formulation, Therapeutic Class, etc.
   */
  MedicationGeneralizationRoleType: v3_RoleCode_MedicationGeneralizationRoleType,
  /**
   * Types of membership for Role code "MBR"
   */
  MemberRoleType: v3_RoleCode_MemberRoleType,
  /**
   * Description:PayorRoleType for a particular type of policy or program benefit package or plan where more detail about the coverage administration role of the Payor is required.  The functions performed by a Payor qualified by a PayorRoleType may be specified by the PayorParticpationFunction value set.
   *
   *
   *                            Examples:A Payor that is a TPA may administer a managed care plan without underwriting the risk.
   */
  PayorRoleType: v3_RoleCode_PayorRoleType,
  /**
   * PersonalRelationshipRoleType
   */
  PersonalRelationshipRoleType: v3_RoleCode_PersonalRelationshipRoleType,
  /**
   * Description: A role recognized through the eligibility of an identified party for benefits covered under an insurance policy or a program based on meeting eligibility criteria.
   *
   *                         Eligibility as a covered party may be conditioned on the party meeting criteria to qualify for coverage under a policy or program, which may be mandated by law.  These criteria may be:
   *
   *
   *
   *                               The sole basis for coverage, e.g., being differently abled may qualify a person for disability coverage
   *
   *
   *
   *                               May more fully qualify a covered party role e.g, being differently abled may qualify an adult child as a dependent
   *
   *
   *
   *                               May impact the level of coverage for a covered party under a policy or program, e.g., being differently abled may qualify a program eligible for additional benefits.
   *
   *
   *
   *
   *                            Discussion:  The Abstract Value Set "CoverageRoleType", which was developed for use in the Canadian realm "pre-coordinate" coverage roles with other roles that a covered party must play in order to be eligible for coverage, e.g., "handicapped dependent".   These role.codes may only be used with COVPTY to avoid overlapping concepts that would result from using them to specify the specializations of COVPTY, e.g., the role.class DEPEN should not be used with the role.code family dependent because that relationship has overlapping concepts due to the role.code precoodination and is conveyed in FICO with the personal relationship role that has a PART role link to the covered party role.  For the same reasons, the role.class DEPEN should not be used with the role.code HANDIC (handicapped dependent); the role.code DIFFABLE (differently abled) should be used instead.
   *
   *                         In summary, the coded concepts in the Abstract Value Set "CoveredPartyRoleType" can be "post-coordinated" with the "RoleClassCoveredParty" Abstract Value Set.  Decoupling these concepts is intended to support an expansive range of covered party concepts and their semantic comparability.
   */
  PolicyOrProgramCoverageRoleType: v3_RoleCode_PolicyOrProgramCoverageRoleType,
  /**
   * Description:A role recognized through the eligibility of a party to play a program eligible for benefits covered or provided under a program.
   */
  ProgramEligiblePartyRoleType: v3_RoleCode_ProgramEligiblePartyRoleType,
  /**
   * Specifies the administrative functionality within a formal experimental design for which the ResearchSubject role was established.  Examples: screening - role is used for pre-enrollment evaluation portion of the design; enrolled - role is used for subjects admitted to the active treatment portion of the design.
   */
  ResearchSubjectRoleBasis: v3_RoleCode_ResearchSubjectRoleBasis,
  /**
   * A role of a place that further classifies the setting (e.g., accident site, road side, work site, community location) in which services are delivered.
   */
  ServiceDeliveryLocationRoleType: v3_RoleCode_ServiceDeliveryLocationRoleType,
  /**
   * SpecimenRoleType
   */
  SpecimenRoleType: v3_RoleCode_SpecimenRoleType,
  /**
   * Description: A role recognized through the eligibility of a party to play a subscriber for benefits covered or provided under a health insurance policy.
   */
  SubscriberCoveredPartyRoleType: v3_RoleCode_SubscriberCoveredPartyRoleType,
  /**
   * Location of an accident where healthcare service was delivered, such as a roadside.
   */
  AccidentSite: v3_RoleCode_AccidentSite,
  /**
   * Description:Identifier assigned to a  location by the organization responsible for accrediting the location.
   */
  AccreditationLocationIdentifier: v3_RoleCode_AccreditationLocationIdentifier,
  /**
   * Description: A person playing the role of program eligible under a program based on active military status.
   *
   *
   *                            Discussion: This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is either "program eligible" or "subscriber" and the persons status as active duty military meets jurisdictional or program criteria.
   */
  ActiveDutyMilitary: v3_RoleCode_ActiveDutyMilitary,
  /**
   * A child taken into one's family through legal means and raised as one's own child.
   */
  AdoptedChild: v3_RoleCode_AdoptedChild_2,
  /**
   * The player of the role (father) is a male who has taken the scoper (child) into their family through legal means and raises them as his own child.
   */
  AdoptiveFather: v3_RoleCode_AdoptiveFather,
  /**
   * The player of the role (father) is a female who has taken the scoper (child) into their family through legal means and raises them as her own child.
   */
  AdoptiveMother: v3_RoleCode_AdoptiveMother,
  /**
   * The player of the role (parent) has taken the scoper (child) into their family through legal means and raises them as his or her own child.
   */
  AdoptiveParent: v3_RoleCode_AdoptiveParent,
  /**
   * Allergy clinic
   */
  AllergyClinic: v3_RoleCode_AllergyClinic,
  /**
   * Location (mobile) where healthcare service was delivered, identified specifically as an ambulance.
   */
  Ambulance: v3_RoleCode_Ambulance,
  /**
   * An entity which corrected, edited, or amended pre-existing information.
   */
  Amender: v3_RoleCode_Amender,
  /**
   * Amputee clinic
   */
  AmputeeClinic: v3_RoleCode_AmputeeClinic,
  /**
   * Description:Non-person living subject used as antibiotic.
   *
   *
   *                            Examples:Bacteriophage, is a virus that infects bacteria.
   */
  Antibiotic: v3_RoleCode_Antibiotic,
  /**
   * Description:Dogs trained to assist the ill or physically challenged.
   */
  AssistiveNonPersonLivingSubject: v3_RoleCode_AssistiveNonPersonLivingSubject,
  /**
   * The player of the role is a sister of the scoping person's mother or father.
   */
  Aunt: v3_RoleCode_Aunt,
  /**
   * Quality Control specimen submitted to the lab whose identity and composition is not known to the lab.
   */
  Blind: v3_RoleCode_Blind,
  /**
   * Cattle used for meat production
   */
  Beef: v3_RoleCode_Beef,
  /**
   * A contact role used to identify a person within a Provider organization that can be contacted for billing purposes (e.g. about the content of the Invoice).
   */
  BillingContact: v3_RoleCode_BillingContact,
  /**
   * Description:Animals, including fish and insects, and microorganisms which may participate as assigned entities in biotherapies.
   */
  BiotherapeuticNonPersonLivingSubject: v3_RoleCode_BiotherapeuticNonPersonLivingSubject,
  /**
   * Chickens raised for meat
   */
  Broiler: v3_RoleCode_Broiler,
  /**
   * Bone marrow transplant clinic
   */
  BoneMarrowTransplantClinic: v3_RoleCode_BoneMarrowTransplantClinic,
  /**
   * Bone marrow transplant unit
   */
  BoneMarrowTransplantUnit: v3_RoleCode_BoneMarrowTransplantUnit,
  /**
   * Breeding/genetic stock
   */
  Breeder: v3_RoleCode_Breeder,
  /**
   * Breast clinic
   */
  BreastClinic: v3_RoleCode_BreastClinic,
  /**
   * The player of the role is a male sharing one or both parents in common with the scoping entity.
   */
  Brother: v3_RoleCode_Brother,
  /**
   * The player of the role is: (1) a brother of the scoping person's spouse, or (2) the husband of the scoping person's sister, or (3) the husband of a sister of the scoping person's spouse.
   */
  BrotherInLaw: v3_RoleCode_BrotherInLaw,
  /**
   * A specimen used for initial calibration settings of an instrument
   */
  Calibrator: v3_RoleCode_Calibrator,
  /**
   * Child and adolescent neurology clinic
   */
  ChildAndAdolescentNeurologyClinic: v3_RoleCode_ChildAndAdolescentNeurologyClinic,
  /**
   * Child and adolescent psychiatry clinic
   */
  ChildAndAdolescentPsychiatryClinic: v3_RoleCode_ChildAndAdolescentPsychiatryClinic,
  /**
   * Ambulatory Health Care Facilities; Clinic/Center; Rehabilitation: Cardiac Facilities
   */
  AmbulatoryHealthCareFacilitiesClinicCenterRehabilitationCardiacFacilities: v3_RoleCode_AmbulatoryHealthCareFacilitiesClinicCenterRehabilitationCardiacFacilities,
  /**
   * A person who has fled his or her home country to find a safe place elsewhere.
   */
  AsylumSeeker: v3_RoleCode_AsylumSeeker,
  /**
   * A person who is someone of below legal age who has fled his or her home country, without his or her parents, to find a safe place elsewhere at time of categorization.
   */
  SingleMinorAsylumSeeker: v3_RoleCode_SingleMinorAsylumSeeker,
  /**
   * Cardiac catheterization lab
   */
  CardiacCatheterizationLab: v3_RoleCode_CardiacCatheterizationLab,
  /**
   * Description:Companion animals, such as dogs, cats, and rabbits, which may be provided to patients to improve general mood, decrease depression and loneliness, and distract from stress-inducing concerns to improve quality of life.
   */
  ClinicalCompanion: v3_RoleCode_ClinicalCompanion,
  /**
   * Coronary care unit
   */
  CoronaryCareUnit: v3_RoleCode_CoronaryCareUnit,
  /**
   * A specialty unit in hospital that focuses on chronic respirator patients and pulmonary failure
   */
  ChestUnit: v3_RoleCode_ChestUnit,
  /**
   * The player of the role is a child of the scoping entity.
   */
  Child: v3_RoleCode_Child,
  /**
   * The player of the role is a child taken into a family through legal means and raised by the scoping person (parent) as his or her own child.
   */
  AdoptedChild_2: v3_RoleCode_AdoptedChild,
  /**
   * The player of the role is a child receiving parental care and nurture from the scoping person (parent) but not related to him or her through legal or blood ties.
   */
  FosterChild: v3_RoleCode_FosterChild,
  /**
   * The player of the role is the spouse of scoping person's child.
   */
  ChildInLaw: v3_RoleCode_ChildInLaw,
  /**
   * (1) A hospital including a physical plant and personnel that provides multidisciplinary diagnosis and treatment for diseases that have one or more of the following characteristics: is permanent; leaves residual disability; is caused by nonreversible pathological alteration; requires special training of the patient for rehabilitation; and/or may be expected to require a long period of supervision or care. In addition, patients require the safety, security, and shelter of these specialized inpatient or partial hospitalization settings. (2) A hospital that provides medical and skilled nursing services to patients with long-term illnesses who are not in an acute phase but who require an intensity of services not available in nursing homes
   */
  ChronicCareFacility: v3_RoleCode_ChronicCareFacility,
  /**
   * A party that makes a claim for coverage under a policy.
   */
  Claimant: v3_RoleCode_Claimant,
  /**
   * An individual authorized to assign an original classification to information, including compilations of unclassified information, based on a determination that the information requires protection against unauthorized disclosure. The individual marks the information with immutable, computable, and human readable security labels in accordance with applicable security labeling policies.  The labeling policies provide instructions on whether and if so how the security labels may be later reclassified [i.e., upgraded, downgraded, used in derivative classification, or declassified] in a manner that preserves the overridden original classification binding and provenance.
   */
  Classifier: v3_RoleCode_Classifier,
  /**
   * A person who is legally recognized as a member of a nation or country, with associated rights and obligations.
   */
  National: v3_RoleCode_National,
  /**
   * A foreigner who is present in a country (which is foreign to him/her) unlawfully or without the country's authorization (may be called an illegal alien).
   */
  NonCountryMemberWithoutResidencePermit: v3_RoleCode_NonCountryMemberWithoutResidencePermit,
  /**
   * A person who is below legal age present in a country, without his or her parents, (which is foreign to him/her) unlawfully or without the country's authorization.
   */
  NonCountryMemberMinorWithoutResidencePermit: v3_RoleCode_NonCountryMemberMinorWithoutResidencePermit,
  /**
   * Companion animals
   */
  Companion: v3_RoleCode_Companion,
  /**
   * Coagulation clinic
   */
  CoagulationClinic: v3_RoleCode_CoagulationClinic,
  /**
   * Description: A person playing the role of an individual insured with continuity of coverage under a policy which is being terminated based on loss of original status that was the basis for coverage.  Criteria for qualifying for continuity of coverage may be set by law.
   *
   *
   *                            Discussion: This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is either "program eligible" or "subscriber" and the person's status as a continuity of coverage beneficiary meets jurisdictional or policy criteria.
   */
  ContinuityOfCoverageBeneficiary: v3_RoleCode_ContinuityOfCoverageBeneficiary,
  /**
   * Community location where healthcare is delivered.
   */
  CommunityLocation: v3_RoleCode_CommunityLocation,
  /**
   * Community Laboratory
   */
  CommunityLaboratory: v3_RoleCode_CommunityLaboratory,
  /**
   * An entity or an entity's delegatee who is the grantee in an agreement such as a consent for services, advanced directive, or a privacy consent directive in accordance with jurisdictional, organizational, or patient policy.
   */
  Consenter: v3_RoleCode_Consenter,
  /**
   * An entity which has witnessed and attests to observing another entity being counseled about an agreement such as a consent for services, advanced directive, or a privacy consent directive.
   */
  ConsentWitness: v3_RoleCode_ConsentWitness,
  /**
   * An entity which participates in the generation of and attest to veracity of content, but is not an author or coauthor. For example a surgeon who is required by institutional, regulatory, or legal rules to sign an operative report, but who was not involved in the authorship of that report.
   */
  CoParticipant: v3_RoleCode_CoParticipant,
  /**
   * The player of the role is a relative of the scoping person descended from a common ancestor, such as a 	grandparent, by two or more steps in a diverging line.
   */
  Cousin: v3_RoleCode_Cousin,
  /**
   * A non-country member admitted to the territory of a nation or country as a non-resident for an explicit purpose.
   */
  PermitCardApplicant: v3_RoleCode_PermitCardApplicant,
  /**
   * Description: A person playing the role of program eligible under a program based on allegations of being the victim of a crime.
   *
   *
   *                            Discussion: This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is "program eligible" and the person's status as a crime victim meets jurisdictional or program criteria.
   */
  CrimeVictim: v3_RoleCode_CrimeVictim,
  /**
   * A foreigner who is a resident of the country but does not have citizenship.
   */
  NonCountryMemberWithResidencePermit: v3_RoleCode_NonCountryMemberWithResidencePermit,
  /**
   * A person who is a resident below legal age of the country without his or her parents and does not have citizenship.
   */
  NonCountryMemberMinorWithResidencePermit: v3_RoleCode_NonCountryMemberMinorWithResidencePermit,
  /**
   * Colon and rectal surgery clinic
   */
  ColonAndRectalSurgeryClinic: v3_RoleCode_ColonAndRectalSurgeryClinic,
  /**
   * Description: A location that plays the role of delivering services which may include providing front-line services to the population of a defined geographic area such as: healthcare services and social services, preventive or curative, rehabilitation or reintegration.
   */
  CommunityServiceCenter: v3_RoleCode_CommunityServiceCenter,
  /**
   * A practice setting where cardiovascular diagnostic procedures or therapeutic interventions are performed (e.g., cardiac catheterization lab, echocardiography suite)
   */
  CardiovascularDiagnosticsOrTherapeuticsUnit: v3_RoleCode_CardiovascularDiagnosticsOrTherapeuticsUnit,
  /**
   * Milk production
   */
  Dairy: v3_RoleCode_Dairy,
  /**
   * Location address where medical supplies were transported to for use.
   */
  DeliveryAddress: v3_RoleCode_DeliveryAddress,
  /**
   * The player of the role is a female offspring of the scoping entity (parent).
   */
  NaturalDaughter: v3_RoleCode_NaturalDaughter,
  /**
   * The player of the role is a female child taken into a family through legal means and raised by the scoping person (parent) as his or her own child.
   */
  AdoptedDaughter: v3_RoleCode_AdoptedDaughter,
  /**
   * Description: The player of the role is a female child (of any type) of scoping entity (parent)
   */
  Daughter: v3_RoleCode_Daughter,
  /**
   * The player of the role is a female child receiving parental care and nurture from the scoping person (parent) but not related to him or her through legal or blood ties.
   */
  FosterDaughter: v3_RoleCode_FosterDaughter,
  /**
   * The player of the role is the wife of scoping person's son.
   */
  DaughterInLaw: v3_RoleCode_DaughterInLaw,
  /**
   * Description:A categorization of medicinal products by their therapeutic properties and/or main therapeutic use.
   */
  TherapeuticClass: v3_RoleCode_TherapeuticClass,
  /**
   * Description:Maggots raised for biodebridement.
   *
   *
   *                            Discussion: Maggot Debridement Therapy is the medical use of live maggots for cleaning non-healing wounds.
   *
   *
   *                            Examples:Removal of burnt skin.
   */
  Debridement: v3_RoleCode_Debridement,
  /**
   * An individual which is authorized to declassify information based on a determination that the information no longer requires protection against unauthorized disclosure.  The individual marks the information being declassified using computable and human readable security labels indicating that this is copy of previously classified information is unclassified in accordance with applicable security labeling policies.  The labeling policies provide instructions on whether and if so how the security labels may be later reclassified [i.e., upgraded or used in derivative classification] in a manner that preserves the overridden original classification binding and provenance.
   */
  Declassifier: v3_RoleCode_Declassifier,
  /**
   * A party to whom some right or authority is granted by a delegator.
   */
  Delegatee: v3_RoleCode_Delegatee,
  /**
   * A party that grants all or some portion its right or authority to another party.
   */
  Delegator: v3_RoleCode_Delegator,
  DEP: v3_RoleCode_DEP,
  /**
   * A party covered under a policy based on association with a subscriber.
   */
  Dependent: v3_RoleCode_Dependent,
  /**
   * Dermatology clinic
   */
  DermatologyClinic: v3_RoleCode_DermatologyClinic,
  /**
   * Description: A person playing the role of program eligible under a program based on meeting criteria for health or functional limitation set by law or by the program.
   *
   *
   *                            Discussion: This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is either "program eligible", "named insured", "individual insured", or "dependent", and the person's status as differently abled meets jurisdictional, policy, or program criteria.
   */
  DifferentlyAbled: v3_RoleCode_DifferentlyAbled,
  /**
   * The player of the role cohabits with the scoping person but is not the scoping person's spouse.
   */
  DomesticPartner: v3_RoleCode_DomesticPartner,
  /**
   * An individual authorized to lower the classification level of labeled content and provide rationale for doing so as directed by a classification guide.
   */
  Downgrader: v3_RoleCode_Downgrader,
  /**
   * A relationship between two people in which one person authorizes another, usually a family member or relative, to act for him or her in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts that is often limited in the kinds of powers that can be assigned.  Unlike ordinary powers of attorney, durable powers can survive for long periods of time, and again, unlike standard powers of attorney, durable powers can continue after incompetency.
   */
  DurablePowerOfAttorney: v3_RoleCode_DurablePowerOfAttorney,
  /**
   * Draft animals
   */
  Draft: v3_RoleCode_Draft,
  /**
   * An individual who is only authorized to classify reproduced, extracted, or summarized classified information, or compile classified and unclassified information by applying classification markings derived from source material or as directed by a classification guide.
   */
  DerivativeClassifier: v3_RoleCode_DerivativeClassifier,
  /**
   * Dual purpose.  Defined purposes based on species and breed
   */
  Dual: v3_RoleCode_Dual,
  /**
   * A practice setting where diagnostic procedures or therapeutic interventions are performed
   */
  DiagnosticsOrTherapeuticsUnit: v3_RoleCode_DiagnosticsOrTherapeuticsUnit,
  /**
   * An electronically simulated QC specimen
   */
  ElectronicQC: v3_RoleCode_ElectronicQC,
  /**
   * Echocardiography lab
   */
  EchocardiographyLab: v3_RoleCode_EchocardiographyLab,
  /**
   * Endocrinology clinic
   */
  EndocrinologyClinic: v3_RoleCode_EndocrinologyClinic,
  /**
   * (X12N 261QD0000N)
   */
  EndoscopyLab: v3_RoleCode_EndoscopyLab,
  /**
   * Description:A payor that is responsible for functions related to the enrollment of covered parties.
   */
  EnrollmentBroker: v3_RoleCode_EnrollmentBroker,
  /**
   * Otorhinolaryngology clinic
   */
  OtorhinolaryngologyClinic: v3_RoleCode_OtorhinolaryngologyClinic,
  /**
   * Epilepsy unit
   */
  EpilepsyUnit: v3_RoleCode_EpilepsyUnit,
  /**
   * The section of a health care facility for providing rapid treatment to victims of sudden illness or trauma.
   */
  EmergencyRoom: v3_RoleCode_EmergencyRoom,
  /**
   * Definition:The specific role being played by a research subject participating in the active treatment or primary data collection portion of a research study.
   */
  Enrollment: v3_RoleCode_Enrollment,
  /**
   * Emergency trauma unit
   */
  EmergencyTraumaUnit: v3_RoleCode_EmergencyTraumaUnit,
  /**
   * The role played by a person acting as the estate executor for a deceased subscriber or policyholder who was the responsible party
   */
  ExecutorOfEstate: v3_RoleCode_ExecutorOfEstate,
  /**
   * Description: A family member not having an immediate genetic or legal relationship e.g. Aunt, cousin, great grandparent, grandchild, grandparent, niece, nephew or uncle.
   */
  ExtendedFamilyMember: v3_RoleCode_ExtendedFamilyMember,
  /**
   * Specimen used for testing proficiency of an organization performing testing (how does this differ from O?)
   */
  FillerProficiency: v3_RoleCode_FillerProficiency,
  /**
   * The player of the role is dependent of the scoping entity.
   */
  FamilyDependent: v3_RoleCode_FamilyDependent,
  /**
   * A relationship between two people characterizing their "familial" relationship
   */
  FamilyMember: v3_RoleCode_FamilyMember,
  /**
   * Animals raised for their fur, hair or skins
   */
  Fiber: v3_RoleCode_Fiber,
  /**
   * A member of the covered party's family. This could be the spouse, a parent, a grand parent, a sibling, etc.
   */
  FamilyMember_2: v3_RoleCode_FamilyMember_2,
  /**
   * Family medicine clinic
   */
  FamilyMedicineClinic: v3_RoleCode_FamilyMedicineClinic,
  /**
   * Player of the role was previously joined to the scoping person in marriage and this marriage is now dissolved and inactive.
   *
   *
   *                            Usage Note: This is significant to indicate as some jurisdictions have different legal requirements for former spouse to access the patient's record, from a general friend.
   */
  FormerSpouse: v3_RoleCode_FormerSpouse,
  /**
   * The player of the role is a person who is known, liked, and trusted by the scoping person.
   */
  UnrelatedFriend: v3_RoleCode_UnrelatedFriend,
  /**
   * Covered party to an insurance policy has coverage through full-time attendance at a recognized educational institution as defined by a particular insurance policy.
   */
  FullTimeStudent: v3_RoleCode_FullTimeStudent,
  /**
   * The player of the role is a male who begets or raises or nurtures the scoping entity (child).
   */
  Father: v3_RoleCode_Father,
  /**
   * The player of the role (parent) who is a male state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterFather: v3_RoleCode_FosterFather,
  /**
   * The player of the role is the father of the scoping person's husband or wife.
   */
  FatherInLaw: v3_RoleCode_FatherInLaw,
  /**
   * The scoper and player were carried in the same womb and share common biological parents but are the product of distinct egg/sperm pairs.
   */
  FraternalTwin: v3_RoleCode_FraternalTwin,
  /**
   * The scoper was carried in the same womb as the male player and shares common biological parents but is the product of a distinct egg/sperm pair.
   */
  FraternalTwinBrother: v3_RoleCode_FraternalTwinBrother,
  /**
   * The scoper was carried in the same womb as the female player and shares common biological parents but is the product of a distinct egg/sperm pair.
   */
  FraternalTwinSister: v3_RoleCode_FraternalTwinSister,
  /**
   * Description:An employer or organization that contracts with an underwriter to assumes the financial risk and administrative responsibility for coverage of health services for covered parties.
   */
  FullyInsuredCoverageSponsor: v3_RoleCode_FullyInsuredCoverageSponsor,
  /**
   * A set of patient samples in which the individuals of the group may or may not be identified.
   */
  Group: v3_RoleCode_Group,
  /**
   * (X12N 282N00000N)
   */
  HospitalsGeneralAcuteCareHospital: v3_RoleCode_HospitalsGeneralAcuteCareHospital,
  /**
   * A child of one's son or daughter.
   */
  Grandchild: v3_RoleCode_Grandchild_2,
  /**
   * Relates a manufactured drug product to the non-proprietary (generic) representation of its ingredients independent of strength, and form.
   *
   *                         The scoping entity identifies a unique combination of medicine ingredients; sometimes referred to as "ingredient set".
   */
  GenericDrug: v3_RoleCode_GenericDrug,
  /**
   * Relates a manufactured drug product to the non-proprietary (generic) representation of its ingredients and dose form, independent of strength of the ingredients. The scoping entity identifies a unique combination of medicine ingredients in a specific dose form.
   */
  GenericDrugForm: v3_RoleCode_GenericDrugForm,
  /**
   * Relates a manufactured drug product to the non-proprietary (generic) representation of is ingredients with their strength.  The scoping entity identifies a unique combination of medicine ingredients with their strength.
   */
  GenericDrugStrength: v3_RoleCode_GenericDrugStrength,
  /**
   * Relates a manufactured drug product to the non-proprietary (generic) representation of its ingredients with their strength in a specific dose form. The scoping entity identifies a unique combination of medicine ingredients with their strength in a single dose form.
   */
  GenericDrugStrengthForm: v3_RoleCode_GenericDrugStrengthForm,
  /**
   * The player is a female whose womb carries the fetus of the scoper.  Generally used when the gestational mother and natural mother are not the same.
   */
  GestationalMother: v3_RoleCode_GestationalMother,
  /**
   * The player of the role is the father of the scoping person's grandparent.
   */
  GreatGrandfather: v3_RoleCode_GreatGrandfather,
  /**
   * The player of the role is the mother of the scoping person's grandparent.
   */
  GreatGrandmother: v3_RoleCode_GreatGrandmother,
  /**
   * The player of the role is a parent of the scoping person's grandparent.
   */
  GreatGrandparent: v3_RoleCode_GreatGrandparent,
  /**
   * Gastroenterology clinic
   */
  GastroenterologyClinic: v3_RoleCode_GastroenterologyClinic,
  /**
   * A practice setting where GI procedures (such as endoscopies) are performed
   */
  GastroenterologyDiagnosticsOrTherapeuticsLab: v3_RoleCode_GastroenterologyDiagnosticsOrTherapeuticsLab,
  /**
   * General internal medicine clinic
   */
  GeneralInternalMedicineClinic: v3_RoleCode_GeneralInternalMedicineClinic,
  /**
   * parent of a parent of the subject.
   */
  Grandparent: v3_RoleCode_Grandparent_2,
  /**
   * An entity which accepts certain rights or authority from a grantor.
   */
  Grantee: v3_RoleCode_Grantee,
  /**
   * An entity which agrees to confer certain rights or authority to a grantee.
   */
  Grantor: v3_RoleCode_Grantor,
  /**
   * The player of the role is the father of the scoping person's mother or father.
   */
  Grandfather: v3_RoleCode_Grandfather,
  /**
   * The player of the role is the mother of the scoping person's mother or father.
   */
  Grandmother: v3_RoleCode_Grandmother,
  /**
   * The player of the role is a child of the scoping person's son or daughter.
   */
  Grandchild_2: v3_RoleCode_Grandchild,
  /**
   * The player of the role is a daughter of the scoping person's son or daughter.
   */
  Granddaughter: v3_RoleCode_Granddaughter,
  /**
   * The player of the role is a son of the scoping person's son or daughter.
   */
  Grandson: v3_RoleCode_Grandson,
  /**
   * The player of the role is a parent of the scoping person's mother or father.
   */
  Grandparent_2: v3_RoleCode_Grandparent,
  /**
   * An individual or organization that makes or gives a promise, assurance, pledge to pay or has paid the healthcare service provider.
   */
  Guarantor: v3_RoleCode_Guarantor,
  /**
   * The role played by a person appointed by the court to look out for the best interests of a minor child during the course of legal proceedings.
   */
  GuardianAdLidem: v3_RoleCode_GuardianAdLidem,
  /**
   * The role played by a person or institution legally empowered with responsibility for the care of a ward.
   */
  Guardian: v3_RoleCode_Guardian,
  /**
   * Gynecology clinic
   */
  GynecologyClinic: v3_RoleCode_GynecologyClinic,
  /**
   * Hand clinic
   */
  HandClinic: v3_RoleCode_HandClinic,
  /**
   * Covered party is a dependent of the policy holder with a physical or mental disability causing a disadvantage that makes independent achievement unusually difficult.
   */
  HandicappedDependent: v3_RoleCode_HandicappedDependent,
  /**
   * The player of the role is a male related to the scoping entity by sharing only one biological parent.
   */
  HalfBrother: v3_RoleCode_HalfBrother,
  /**
   * Hemodialysis unit
   */
  HemodialysisUnit: v3_RoleCode_HemodialysisUnit,
  /**
   * Hematology clinic
   */
  HematologyClinic: v3_RoleCode_HematologyClinic,
  /**
   * Description: A location that plays the role of delivering services which may include tests done based on clinical specimens to get health information about a patient as pertaining to the diagnosis, treatment and prevention of disease.  Hospital laboratories may be further divided into specialized units such as Anatomic Pathology, Microbiology, and Biochemistry.
   */
  HospitalLaboratory: v3_RoleCode_HospitalLaboratory,
  /**
   * Home Health
   */
  HomeHealth: v3_RoleCode_HomeHealth,
  /**
   * An acute care institution that provides medical, surgical, or psychiatric care and treatment for the sick or the injured.
   */
  Hospital: v3_RoleCode_Hospital,
  /**
   * A relationship between two people in which one person authorizes another to act for him or her in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts that continues (by its terms) to be effective even though the grantor has become mentally incompetent after signing the document.
   */
  HealthcarePowerOfAttorney: v3_RoleCode_HealthcarePowerOfAttorney,
  /**
   * Description: A location that plays the role of delivering services which may include the branch of medicine that uses ionizing and non-ionizing radiation to diagnose and treat diseases.  The radiology unit may be further divided into subspecialties such as Imaging, Cardiovascular, Thoracic, and Ultrasound.
   */
  RadiologyUnit: v3_RoleCode_RadiologyUnit,
  /**
   * The player of the role is related to the scoping entity by sharing only one biological parent.
   */
  HalfSibling: v3_RoleCode_HalfSibling,
  /**
   * The player of the role is a female related to the scoping entity by sharing only one biological parent.
   */
  HalfSister: v3_RoleCode_HalfSister,
  /**
   * Hypertension clinic
   */
  HypertensionClinic: v3_RoleCode_HypertensionClinic,
  /**
   * Hospital unit
   */
  HospitalUnit: v3_RoleCode_HospitalUnit,
  /**
   * The player of the role is a man joined to a woman (scoping person) in marriage.
   */
  Husband: v3_RoleCode_Husband,
  /**
   * Description: A location that plays the role of delivering services which may include collecting specimens and/or samples from patients for laboratory testing purposes, but does not perform any tests or analysis functions.
   */
  SpecimenCollectionSite: v3_RoleCode_SpecimenCollectionSite,
  /**
   * Intensive care unit
   */
  IntensiveCareUnit: v3_RoleCode_IntensiveCareUnit,
  /**
   * Focuses on assessing disability
   */
  ImpairmentEvaluationCenter: v3_RoleCode_ImpairmentEvaluationCenter,
  /**
   * Description: A person playing the role of program eligible under a program based on aboriginal ancestry or as a member of an aboriginal community.
   *
   *
   *                            Discussion: This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is "program eligible" and the person's status as a member of an indigenous people meets jurisdictional or program criteria.
   */
  MemberOfAnIndigenousPeople: v3_RoleCode_MemberOfAnIndigenousPeople,
  /**
   * A party covered under a policy as the policyholder.
   */
  Individual: v3_RoleCode_Individual,
  /**
   * Infectious disease clinic
   */
  InfectiousDiseaseClinic: v3_RoleCode_InfectiousDiseaseClinic,
  /**
   * Covered party is an injured party with a legal claim for compensation against a policy holder under an insurance policy.
   */
  InjuredPlaintiff: v3_RoleCode_InjuredPlaintiff,
  /**
   * Description: A person playing the role of program eligible under a workers compensation program based on the filing of work-related injury claim.
   *
   *
   *                            Discussion:  This CoveredPartyRoleType.code is used when the CoveredPartyRole class code is either "program eligible", a "named insured", and "individual insured",  or "dependent", and the person's status as differently abled or "handicapped" meets jurisdictional, policy, or program criteria.
   */
  InjuredWorker: v3_RoleCode_InjuredWorker,
  /**
   * Description: A location that plays the role of delivering services which may include tests are done on clinical specimens to get health information about a patient pertaining to the diagnosis, treatment, and prevention of disease for a hospital visit longer than one day.
   */
  InpatientLaboratory: v3_RoleCode_InpatientLaboratory,
  /**
   * A relationship between an individual and a member of their spousal partner's immediate family.
   */
  Inlaw: v3_RoleCode_Inlaw,
  /**
   * Description: A location that plays the role of delivering services which may include providing judicious, safe, efficacious, appropriate and cost effective use of medicines for treatment of patients for visits longer than one day. The distinction between inpatient pharmacies and retail (or outpatient) pharmacies is that they are part of a patient's continuity of care while staying in the hospital.
   */
  InpatientPharmacy: v3_RoleCode_InpatientPharmacy,
  /**
   * An entity which converts spoken or written language into the language of key participants in an event such as when a provider is obtaining a patient's consent to treatment or permission to disclose information.
   */
  Interpreter: v3_RoleCode_Interpreter,
  /**
   * Infertility clinic
   */
  InfertilityClinic: v3_RoleCode_InfertilityClinic,
  /**
   * The scoper and player are offspring of the same egg-sperm pair.
   */
  IdenticalTwin: v3_RoleCode_IdenticalTwin,
  /**
   * The male scoper is an offspring of the same egg-sperm pair as the male player.
   */
  IdenticalTwinBrother: v3_RoleCode_IdenticalTwinBrother,
  /**
   * The female scoper is an offspring of the same egg-sperm pair as the female player.
   */
  IdenticalTwinSister: v3_RoleCode_IdenticalTwinSister,
  /**
   * Description:Identifier assigned to a location by a jurisdiction.
   */
  JurisdictionLocationIdentifier: v3_RoleCode_JurisdictionLocationIdentifier,
  /**
   * Aliquots of individual specimens combined to form a single specimen representing all of the included individuals.
   */
  Pool: v3_RoleCode_Pool,
  /**
   * Laboratory
   */
  Laboratory: v3_RoleCode_Laboratory,
  /**
   * Description:Identifier assigned to a  location by a local party (which could be the facility itself or organization overseeing a group of facilities).
   */
  LocalLocationIdentifier: v3_RoleCode_LocalLocationIdentifier,
  /**
   * Chickens raised for egg production
   */
  Layer: v3_RoleCode_Layer,
  /**
   * Lympedema clinic
   */
  LympedemaClinic: v3_RoleCode_LympedemaClinic,
  /**
   * Description:The player of the role is a biological sister of the scoping person's biological mother.
   */
  MaternalAunt: v3_RoleCode_MaternalAunt,
  /**
   * Description: A location that plays the role of delivering services which include biochemistry, hematology, microbiology, immunochemistry, and toxicology.
   */
  MedicalLaboratory: v3_RoleCode_MedicalLaboratory,
  /**
   * Description:The player of the role is a biological relative of the scoping person descended from a common ancestor on the player's mother's side, such as a grandparent, by two or more steps in a diverging line.
   */
  MaternalCousin: v3_RoleCode_MaternalCousin,
  /**
   * Relates a manufactured drug product to the non-proprietary (generic) representation of its ingredients with their strength in a specific dose form. The scoping entity identifies a unique combination of medicine ingredients with their strength in a single dose form.
   */
  ManufacturedDrugStrengthForm: v3_RoleCode_ManufacturedDrugStrengthForm,
  /**
   * Medical genetics clinic
   */
  MedicalGeneticsClinic: v3_RoleCode_MedicalGeneticsClinic,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological mother's parent.
   */
  MaternalGreatGrandfather: v3_RoleCode_MaternalGreatGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological mother's parent.
   */
  MaternalGreatGrandmother: v3_RoleCode_MaternalGreatGrandmother,
  /**
   * Description:The player of the role is a biological parent of the scoping person's biological mother's parent.
   */
  MaternalGreatGrandparent: v3_RoleCode_MaternalGreatGrandparent,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological mother.
   */
  MaternalGrandfather: v3_RoleCode_MaternalGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological mother.
   */
  MaternalGrandmother: v3_RoleCode_MaternalGrandmother,
  /**
   * Description:The player of the role is the biological parent of the scoping person's biological mother.
   */
  MaternalGrandparent: v3_RoleCode_MaternalGrandparent,
  /**
   * A health care facility operated by the Department of Defense or other military operation.
   */
  MilitaryHospital: v3_RoleCode_MilitaryHospital,
  /**
   * Definition: A person playing the role of program eligible under a program based on military status.
   *
   *
   *                            Discussion:  This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is either "program eligible" or "subscriber" and the person's status as a member of the military meets jurisdictional or program criteria
   */
  Military: v3_RoleCode_Military,
  /**
   * Location (mobile) where healthcare service was delivered.
   */
  MobileUnit: v3_RoleCode_MobileUnit,
  /**
   * Animals raised for meat production
   */
  Meat: v3_RoleCode_Meat,
  /**
   * The player of the role is a female who conceives, gives birth to, or raises and nurtures the scoping entity (child).
   */
  Mother: v3_RoleCode_Mother,
  /**
   * The player of the role (parent) who is a female state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterMother: v3_RoleCode_FosterMother,
  /**
   * The player of the role is the mother of the scoping person's husband or wife.
   */
  MotherInLaw: v3_RoleCode_MotherInLaw,
  /**
   * Poultry flocks used for chick/poult production
   */
  Multiplier: v3_RoleCode_Multiplier,
  /**
   * Description:The player of the role is a biological brother of the scoping person's biological mother.
   */
  MaternalUncle: v3_RoleCode_MaternalUncle,
  /**
   * A party to an insurance policy to whom the insurer agrees to indemnify for losses, provides benefits for, or renders services.
   */
  NamedInsured: v3_RoleCode_NamedInsured,
  /**
   * A child as determined by birth.
   */
  NaturalChild: v3_RoleCode_NaturalChild_2,
  /**
   * The player of the role lives near or next to the 	scoping person.
   */
  Neighbor: v3_RoleCode_Neighbor,
  /**
   * The player of the role is a male having the same biological parents as the scoping entity.
   */
  NaturalBrother: v3_RoleCode_NaturalBrother,
  /**
   * Nursing or custodial care facility
   */
  NursingOrCustodialCareFacility: v3_RoleCode_NursingOrCustodialCareFacility,
  /**
   * Neurology critical care and stroke unit
   */
  NeurologyCriticalCareAndStrokeUnit: v3_RoleCode_NeurologyCriticalCareAndStrokeUnit,
  /**
   * The player of the role is an offspring of the scoping entity as determined by birth.
   */
  NaturalChild_2: v3_RoleCode_NaturalChild,
  /**
   * Nephrology clinic
   */
  NephrologyClinic: v3_RoleCode_NephrologyClinic,
  /**
   * The player of the role is a son of the scoping person's brother or sister or of the brother or sister of the 	scoping person's spouse.
   */
  Nephew: v3_RoleCode_Nephew,
  /**
   * Neurology clinic
   */
  NeurologyClinic: v3_RoleCode_NeurologyClinic,
  /**
   * The player of the role is a male who begets the scoping entity (child).
   */
  NaturalFather: v3_RoleCode_NaturalFather,
  /**
   * Indicates the biologic male parent of a fetus.
   */
  NaturalFatherOfFetus: v3_RoleCode_NaturalFatherOfFetus,
  /**
   * The player of the role is a daughter of the scoping person's brother or sister or of the brother or sister of the 	scoping person's spouse.
   */
  Niece: v3_RoleCode_Niece,
  /**
   * A child of one's brother or sister or of the brother or sister of one's spouse.
   */
  NieceNephew: v3_RoleCode_NieceNephew_2,
  /**
   * The player of the role is a child of scoping person's brother or sister or of the brother or sister of the 	scoping person's spouse.
   */
  NieceNephew_2: v3_RoleCode_NieceNephew,
  /**
   * The player of the role is a female who conceives or gives birth to the scoping entity (child).
   */
  NaturalMother: v3_RoleCode_NaturalMother,
  /**
   * The player is the biologic female parent of the scoping fetus.
   */
  NaturalMotherOfFetus: v3_RoleCode_NaturalMotherOfFetus,
  /**
   * natural parent
   */
  NaturalParent: v3_RoleCode_NaturalParent,
  /**
   * Neurosurgery unit
   */
  NeurosurgeryUnit: v3_RoleCode_NeurosurgeryUnit,
  /**
   * The player of the role has both biological parents in common with the scoping entity.
   */
  NaturalSibling: v3_RoleCode_NaturalSibling,
  /**
   * The player of the role is a female having the same biological parents as the scoping entity.
   */
  NaturalSister: v3_RoleCode_NaturalSister,
  /**
   * A specimen used for evaluation of operator proficiency (operator in what context?)
   */
  OperatorProficiency: v3_RoleCode_OperatorProficiency,
  /**
   * Obstetrics clinic
   */
  ObstetricsClinic: v3_RoleCode_ObstetricsClinic,
  /**
   * Outpatient facility
   */
  OutpatientFacility: v3_RoleCode_OutpatientFacility,
  /**
   * Oral and maxillofacial surgery clinic
   */
  OralAndMaxillofacialSurgeryClinic: v3_RoleCode_OralAndMaxillofacialSurgeryClinic,
  /**
   * Medical oncology clinic
   */
  MedicalOncologyClinic: v3_RoleCode_MedicalOncologyClinic,
  /**
   * The relationship that a person has with his or her self.
   */
  Self: v3_RoleCode_Self,
  /**
   * Opthalmology clinic
   */
  OpthalmologyClinic: v3_RoleCode_OpthalmologyClinic,
  /**
   * Description: A location that plays the role of delivering services which may include examination, diagnosis, treatment, management, and prevention of diseases and disorders of the eye as well as prescribing and fitting appropriate corrective lenses (glasses or contact lenses) as needed.  Optometry clinics may also provide tests for visual field screening, measuring intra-ocular pressure and ophthalmoscopy, as and when required.
   */
  OptometryClinic: v3_RoleCode_OptometryClinic,
  /**
   * A contact for an organization for administrative purposes. Contact role specifies a person acting as a liason for the organization.
   *
   *                         Example: HR Department representative.
   */
  OrganizationalContact: v3_RoleCode_OrganizationalContact,
  /**
   * Orthopedics clinic
   */
  OrthopedicsClinic: v3_RoleCode_OrthopedicsClinic,
  /**
   * Description: A location that plays the role of delivering services which may include tests are done on clinical specimens to get health information about a patient pertaining to the diagnosis, treatment, and prevention of disease for same day visits.
   */
  OutpatientLaboratory: v3_RoleCode_OutpatientLaboratory,
  /**
   * Description: A location that plays the role of delivering services which may include providing judicious, safe, efficacious, appropriate and cost effective use of medicines for treatment of patients for outpatient visits and may also be used for discharge prescriptions.
   */
  OutpatientPharmacy: v3_RoleCode_OutpatientPharmacy,
  /**
   * A specimen that has been collected from a patient.
   */
  Patient: v3_RoleCode_Patient,
  /**
   * (X12N 261QP3300N)
   */
  PainClinic: v3_RoleCode_PainClinic,
  /**
   * One that begets or brings forth offspring or a person who brings up and cares for for another (Webster's Collegiate Dictionary)
   */
  Parent: v3_RoleCode_Parent_2,
  /**
   * Pathologist
   */
  Pathologist: v3_RoleCode_Pathologist,
  /**
   * Description:The player of the role is a biological sister of the scoping person's biological father.
   */
  PaternalAunt: v3_RoleCode_PaternalAunt,
  /**
   * A contact role used to identify a person within a Payor organization to whom this communication is addressed.
   */
  PayorContact: v3_RoleCode_PayorContact,
  /**
   * (X12N 261QP2300N)
   */
  PrimaryCareClinic: v3_RoleCode_PrimaryCareClinic,
  /**
   * Description:The player of the role is a biological relative of the scoping person descended from a common ancestor on the player's father's side, such as a grandparent, by two or more steps in a diverging line.
   */
  PaternalCousin: v3_RoleCode_PaternalCousin,
  /**
   * Pediatrics clinic
   */
  PediatricsClinic: v3_RoleCode_PediatricsClinic,
  /**
   * Pediatric cardiology clinic
   */
  PediatricCardiologyClinic: v3_RoleCode_PediatricCardiologyClinic,
  /**
   * Pediatric endocrinology clinic
   */
  PediatricEndocrinologyClinic: v3_RoleCode_PediatricEndocrinologyClinic,
  /**
   * Pediatric gastroenterology clinic
   */
  PediatricGastroenterologyClinic: v3_RoleCode_PediatricGastroenterologyClinic,
  /**
   * Pediatric hematology clinic
   */
  PediatricHematologyClinic: v3_RoleCode_PediatricHematologyClinic,
  /**
   * Pediatric oncology clinic
   */
  PediatricOncologyClinic: v3_RoleCode_PediatricOncologyClinic,
  /**
   * Pediatric intensive care unit
   */
  PediatricIntensiveCareUnit: v3_RoleCode_PediatricIntensiveCareUnit,
  /**
   * Pediatric infectious disease clinic
   */
  PediatricInfectiousDiseaseClinic: v3_RoleCode_PediatricInfectiousDiseaseClinic,
  /**
   * Pediatric nephrology clinic
   */
  PediatricNephrologyClinic: v3_RoleCode_PediatricNephrologyClinic,
  /**
   * Pediatric neonatal intensive care unit
   */
  PediatricNeonatalIntensiveCareUnit: v3_RoleCode_PediatricNeonatalIntensiveCareUnit,
  /**
   * Pediatric rheumatology clinic
   */
  PediatricRheumatologyClinic: v3_RoleCode_PediatricRheumatologyClinic,
  /**
   * Pediatric unit
   */
  PediatricUnit: v3_RoleCode_PediatricUnit,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological father's parent.
   */
  PaternalGreatGrandfather: v3_RoleCode_PaternalGreatGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological father's parent.
   */
  PaternalGreatGrandmother: v3_RoleCode_PaternalGreatGrandmother,
  /**
   * Description:The player of the role is a biological parent of the scoping person's biological father's parent.
   */
  PaternalGreatGrandparent: v3_RoleCode_PaternalGreatGrandparent,
  /**
   * Description:The player of the role is the biological father of the scoping person's biological father.
   */
  PaternalGrandfather: v3_RoleCode_PaternalGrandfather,
  /**
   * Description:The player of the role is the biological mother of the scoping person's biological father.
   */
  PaternalGrandmother: v3_RoleCode_PaternalGrandmother,
  /**
   * Description:The player of the role is the biological parent of the scoping person's biological father.
   */
  PaternalGrandparent: v3_RoleCode_PaternalGrandparent,
  /**
   * Policy holder for the insurance policy.
   */
  PolicyHolder: v3_RoleCode_PolicyHolder,
  /**
   * Location where healthcare service was delivered, identified as a pharmacy.
   */
  Pharmacy: v3_RoleCode_Pharmacy,
  /**
   * Phlebotomist
   */
  Phlebotomist: v3_RoleCode_Phlebotomist,
  /**
   * (X12N 273R00000N)
   */
  PsychiatricHospitalUnit: v3_RoleCode_PsychiatricHospitalUnit,
  /**
   * Animals rasied for recreation
   */
  Pleasure: v3_RoleCode_Pleasure,
  /**
   * Plastic surgery clinic
   */
  PlasticSurgeryClinic: v3_RoleCode_PlasticSurgeryClinic,
  /**
   * (X12N 261QP1100N)
   */
  PodiatryClinic: v3_RoleCode_PodiatryClinic,
  /**
   * A relationship between two people in which one person authorizes another to act for him in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts.
   */
  PowerOfAttorney: v3_RoleCode_PowerOfAttorney,
  /**
   * Pain rehabilitation center
   */
  PainRehabilitationCenter: v3_RoleCode_PainRehabilitationCenter,
  /**
   * Preventive medicine clinic
   */
  PreventiveMedicineClinic: v3_RoleCode_PreventiveMedicineClinic,
  /**
   * The player of the role is one who begets, gives birth to, or nurtures and raises the scoping entity (child).
   */
  Parent_2: v3_RoleCode_Parent,
  /**
   * The player of the role (parent) who is a state-certified caregiver responsible for the scoper (child) who has been placed in the parent's care. The placement of the child is usually arranged through the government or a social-service agency, and temporary.
   *
   *                         The state, via a jurisdiction recognized child protection agency, stands as in loco parentis to the child, making all legal decisions while the foster parent is responsible for the day-to-day care of the specified child.
   */
  FosterParent: v3_RoleCode_FosterParent,
  /**
   * The player of the role is the parent of scoping person's husband or wife.
   */
  ParentInLaw: v3_RoleCode_ParentInLaw,
  /**
   * Proctology clinic
   */
  ProctologyClinic: v3_RoleCode_ProctologyClinic,
  /**
   * Location where healthcare service was delivered, identified as the healthcare provider's practice office.
   */
  ProviderQuoteSOffice: v3_RoleCode_ProviderQuoteSOffice,
  /**
   * A party that meets the eligibility criteria for coverage under a program.
   */
  ProgramEligible: v3_RoleCode_ProgramEligible,
  /**
   * Prosthodontics clinic
   */
  ProsthodonticsClinic: v3_RoleCode_ProsthodonticsClinic,
  /**
   * Psychology clinic
   */
  PsychologyClinic: v3_RoleCode_PsychologyClinic,
  /**
   * Covered party to an insurance policy has coverage through part-time attendance at a recognized educational institution as defined by a particular insurance policy.
   */
  PartTimeStudent: v3_RoleCode_PartTimeStudent,
  /**
   * Psychiatry clinic
   */
  PsychiatryClinic: v3_RoleCode_PsychiatryClinic,
  PSYCHCF: v3_RoleCode_PSYCHCF,
  /**
   * Healthcare facility that cares for patients with psychiatric illness(s).
   */
  PsychatricCareFacility: v3_RoleCode_PsychatricCareFacility,
  /**
   * The recipient for the service(s) and/or product(s) when they are not the covered party.
   */
  Patient_2: v3_RoleCode_Patient_2,
  /**
   * location where healthcare was delivered which is the residence of the Patient.
   */
  PatientQuoteSResidence: v3_RoleCode_PatientQuoteSResidence,
  /**
   * Description:The player of the role is a biological brother of the scoping person's biological father.
   */
  PaternalUncle: v3_RoleCode_PaternalUncle,
  /**
   * A specimen specifically used to verify the sensitivity, specificity, accuracy or other perfomance parameter of a diagnostic test.
   */
  QualityControl: v3_RoleCode_QualityControl,
  /**
   * A portion of an original patent sample that is tested at the same time as the original sample
   */
  Replicate: v3_RoleCode_Replicate,
  /**
   * A practice setting where radiology services (diagnostic or therapeutic) are provided            (X12N 261QR0200N)
   */
  RadiologyDiagnosticsOrTherapeuticsUnit: v3_RoleCode_RadiologyDiagnosticsOrTherapeuticsUnit,
  /**
   * (X12N 261QX0203N)
   */
  RadiationOncologyUnit: v3_RoleCode_RadiationOncologyUnit,
  /**
   * Animals raised for racing perfomance
   */
  Racing: v3_RoleCode_Racing,
  /**
   * The role played by a party who has legal responsibility for another party.
   */
  ResponsibleParty: v3_RoleCode_ResponsibleParty,
  /**
   * Description: A person playing the role of an individual insured under a policy based on meeting criteria for the employment status of retired set by law or the policy.
   *
   *
   *                            Discussion: This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is either "program eligible" or "subscriber" and the person's status as a retiree meets jurisdictional or policy criteria.
   */
  Retiree: v3_RoleCode_Retiree,
  /**
   * Description: A person playing the role of program eligible under a program based on retired military status.
   *
   *
   *                            Discussion: This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is either "program eligible" or "subscriber" and the persons status as retired military meets jurisdictional or program criteria.
   */
  RetiredMilitary: v3_RoleCode_RetiredMilitary,
  /**
   * An entity authorized to filter information according to approved criteria.
   */
  Reviewer: v3_RoleCode_Reviewer,
  /**
   * (X12N 283X00000N)
   */
  RehabilitationHospital: v3_RoleCode_RehabilitationHospital,
  /**
   * Description: A location that plays the role of delivering services which may include life training and/or social support to people with addictions.
   */
  AddictionTreatmentCenter: v3_RoleCode_AddictionTreatmentCenter,
  /**
   * Rheumatology clinic
   */
  RheumatologyClinic: v3_RoleCode_RheumatologyClinic,
  /**
   * Description: A location that plays the role of delivering services which may include adaptation, rehabilitation and social integration services for people with intellectual and/or pervasive development disorders such as autism or severe behaviour disorder.
   */
  IntellectualImpairmentCenter: v3_RoleCode_IntellectualImpairmentCenter,
  /**
   * Description: A location that plays the role of delivering services which may social support services for adolescents who are pregnant or have child and are experiencing adaptation issues/difficulties in their current or eventual parenting role.
   */
  ParentsWithAdjustmentDifficultiesCenter: v3_RoleCode_ParentsWithAdjustmentDifficultiesCenter,
  /**
   * Description: A location that plays the role of delivering services which may include adaptation, rehabilitation and social integration services for people with physical impairments.
   */
  PhysicalImpairmentCenter: v3_RoleCode_PhysicalImpairmentCenter,
  /**
   * Description: A location that plays the role of delivering services for people with hearing impairments.
   */
  PhysicalImpairmentHearingCenter: v3_RoleCode_PhysicalImpairmentHearingCenter,
  /**
   * Description: A location that plays the role of delivering services for people with motor skill impairments.
   */
  PhysicalImpairmentMotorSkillsCenter: v3_RoleCode_PhysicalImpairmentMotorSkillsCenter,
  /**
   * Description: A location that plays the role of delivering services for people with visual skill impairments.
   */
  PhysicalImpairmentVisualSkillsCenter: v3_RoleCode_PhysicalImpairmentVisualSkillsCenter,
  /**
   * Rehabilitation hospital unit
   */
  RehabilitationHospitalUnit: v3_RoleCode_RehabilitationHospitalUnit,
  /**
   * Description: A location that plays the role of delivering services which may include life training and/or social support services for the adaption, rehabilitation and social integration of youths with adjustment difficulties.
   */
  YouthsWithAdjustmentDifficultiesCenter: v3_RoleCode_YouthsWithAdjustmentDifficultiesCenter,
  /**
   * Neuroradiology unit
   */
  NeuroradiologyUnit: v3_RoleCode_NeuroradiologyUnit,
  /**
   * One who shares living quarters with the subject.
   */
  Roommate: v3_RoleCode_Roommate,
  /**
   * Residential treatment facility
   */
  ResidentialTreatmentFacility: v3_RoleCode_ResidentialTreatmentFacility,
  /**
   * Location where healthcare service was delivered, identified as a school or educational facility.
   */
  School: v3_RoleCode_School,
  /**
   * Definition:The specific role being played by a research subject participating in the pre-enrollment evaluation portion of  a research study.
   */
  Screening: v3_RoleCode_Screening,
  /**
   * Description:Dogs trained to assist persons who are seeing impaired or blind.
   */
  Seeing: v3_RoleCode_Seeing,
  /**
   * Covered party is the policy holder.  Also known as the subscriber.
   */
  Self_2: v3_RoleCode_Self_2,
  /**
   * Description:An employer or organization that assumes the financial risk and administrative responsibility for coverage of health services for covered parties.
   */
  SelfInsuredCoverageSponsor: v3_RoleCode_SelfInsuredCoverageSponsor,
  /**
   * Animals raised for shows
   */
  Show: v3_RoleCode_Show,
  /**
   * The player of the role shares one or both parents in common with the scoping entity.
   */
  Sibling: v3_RoleCode_Sibling,
  /**
   * The player of the role is: (1) a sibling of the scoping person's spouse, or (2) the spouse of the scoping person's sibling, or (3) the spouse of a sibling of the scoping person's spouse.
   */
  SiblingInLaw: v3_RoleCode_SiblingInLaw,
  /**
   * A person who is important to one's well being; especially a spouse or one in a similar relationship.  (The player is the one who is important)
   */
  SignificantOther: v3_RoleCode_SignificantOther,
  /**
   * The player of the role is a female sharing one or both parents in common with the scoping entity.
   */
  Sister: v3_RoleCode_Sister,
  /**
   * The player of the role is: (1) a sister of the scoping person's spouse, or (2) the wife of the scoping person's brother, or (3) the wife of a brother of the scoping person's spouse.
   */
  SisterInLaw: v3_RoleCode_SisterInLaw,
  /**
   * (X12N 261QA1200N)
   */
  SleepDisordersUnit: v3_RoleCode_SleepDisordersUnit,
  /**
   * (X12N 314000000N)
   */
  SkilledNursingFacility: v3_RoleCode_SkilledNursingFacility,
  /**
   * Description:Dogs trained or having the ability to detect imminent seizures or cancers in humans, probably as a result of volatile chemical (odors) given off by the malignancy of the host.
   */
  Sniffing: v3_RoleCode_Sniffing,
  /**
   * The player of the role is a male offspring of the scoping entity (parent).
   */
  NaturalSon: v3_RoleCode_NaturalSon,
  /**
   * The player of the role is a male child taken into a family through legal means and raised by the scoping person (parent) as his or her own child.
   */
  AdoptedSon: v3_RoleCode_AdoptedSon,
  /**
   * Description: The player of the role is a male child (of any type) of scoping entity (parent)
   */
  Son: v3_RoleCode_Son,
  /**
   * The player of the role is a male child receiving parental care and nurture from the scoping person (parent) but not related to him or her through legal or blood ties.
   */
  FosterSon: v3_RoleCode_FosterSon,
  /**
   * The player of the role is the husband of scoping person's daughter.
   */
  SonInLaw: v3_RoleCode_SonInLaw,
  /**
   * Sports medicine clinic
   */
  SportsMedicineClinic: v3_RoleCode_SportsMedicineClinic,
  /**
   * Covered party is an individual that the policy holder has assumed responsibility for, such as foster child or legal ward.
   */
  SponsoredDependent: v3_RoleCode_SponsoredDependent,
  /**
   * A relationship between two people in which one person authorizes another to act for him or her in a manner which is a legally binding upon the person giving such authority as if he or she personally were to do the acts that is often limited in the kinds of powers that can be assigned.
   */
  SpecialPowerOfAttorney: v3_RoleCode_SpecialPowerOfAttorney,
  /**
   * The player of the role is a marriage partner of the scoping person.
   */
  Spouse: v3_RoleCode_Spouse,
  /**
   * A marriage partner; a husband or wife.
   */
  Spouse_2: v3_RoleCode_Spouse_2,
  /**
   * A child receiving parental care and nurture from a person who is related to them through marriage to their parent.
   */
  StepChild: v3_RoleCode_StepChild_2,
  /**
   * The player of the role is a son of the scoping person's stepparent.
   */
  Stepbrother: v3_RoleCode_Stepbrother,
  /**
   * The player of the role is a child of the scoping person's spouse by a previous union.
   */
  StepChild_2: v3_RoleCode_StepChild,
  /**
   * The player of the role is a daughter of the scoping person's spouse by a previous union.
   */
  Stepdaughter: v3_RoleCode_Stepdaughter,
  /**
   * The player of the role is the husband of scoping person's mother and not the scoping person's natural father.
   */
  Stepfather: v3_RoleCode_Stepfather,
  /**
   * The player of the role is the wife of scoping person's father and not the scoping person's natural mother.
   */
  Stepmother: v3_RoleCode_Stepmother,
  /**
   * The player of the role is the spouse of the scoping person's parent and not the scoping person's natural parent.
   */
  StepParent: v3_RoleCode_StepParent,
  /**
   * The player of the role is a child of the scoping person's stepparent.
   */
  StepSibling: v3_RoleCode_StepSibling,
  /**
   * The player of the role is a daughter of the scoping person's stepparent.
   */
  Stepsister: v3_RoleCode_Stepsister,
  /**
   * The player of the role is a son of the scoping person's spouse by a previous union.
   */
  Stepson: v3_RoleCode_Stepson,
  /**
   * Covered party to an insurance policy has coverage through full-time or part-time attendance at a recognized educational institution as defined by a particular insurance policy.
   */
  Student: v3_RoleCode_Student,
  /**
   * Surgery clinic
   */
  SurgeryClinic: v3_RoleCode_SurgeryClinic,
  /**
   * Self
   */
  Self_3: v3_RoleCode_Self_3,
  /**
   * A party covered under a policy based on association with a sponsor who is the policy holder, and whose association may provide for the eligibility of dependents for coverage
   */
  Subscriber: v3_RoleCode_Subscriber,
  /**
   * (X12N 324500000N)
   */
  SubstanceUseRehabilitationFacility: v3_RoleCode_SubstanceUseRehabilitationFacility,
  /**
   * Third Party
   */
  ThirdParty: v3_RoleCode_ThirdParty,
  /**
   * Description:Third party administrator (TPA) is a payor organization that processes health care claims without carrying insurance risk. Third party administrators are prominent players in the managed care industry and have the expertise and capability to administer all or a portion of the claims process. They are normally contracted by a health insurer or self-insuring companies to administer services, including claims administration, premium collection, enrollment and other administrative activities.
   *
   *                         Self-insured employers often contract with third party administrator to handle their insurance functions. Insurance companies oftentimes outsource the claims, utilization review or membership functions to a TPA. Sometimes TPAs only manage provider networks, only claims or only utilization review.
   *
   *                         While some third-party administrators may operate as units of insurance companies, they are often independent. However, hospitals or provider organizations desiring to set up their own health plans will often outsource certain responsibilities to TPAs.  TPAs may perform one or several payor functions, specified by the PayorParticipationFunction value set, such as provider management, enrollment, utilization management, and fee for service claims adjudication management.
   */
  ThirdPartyAdministrator: v3_RoleCode_ThirdPartyAdministrator,
  /**
   * Transplant clinic
   */
  TransplantClinic: v3_RoleCode_TransplantClinic,
  /**
   * Travel and geographic medicine clinic
   */
  TravelAndGeographicMedicineClinic: v3_RoleCode_TravelAndGeographicMedicineClinic,
  /**
   * A person who is a member of a tribe.
   */
  TribalMember: v3_RoleCode_TribalMember,
  /**
   * The scoper and player were carried in the same womb and shared common biological parents.
   */
  Twin: v3_RoleCode_Twin,
  /**
   * The scoper was carried in the same womb as the male player and shares common biological parents.
   */
  TwinBrother: v3_RoleCode_TwinBrother,
  /**
   * The scoper was carried in the same womb as the female player and shares common biological parents.
   */
  TwinSister: v3_RoleCode_TwinSister,
  /**
   * Description:A payor that is responsible for review and case management of health services covered under a policy or program.
   */
  UtilizationManagementOrganization: v3_RoleCode_UtilizationManagementOrganization,
  /**
   * The player of the role is a brother of the scoping person's mother or father.
   */
  Uncle: v3_RoleCode_Uncle,
  /**
   * Description: A location that plays the role of delivering services which may include: social emergency services required for a young person as required under any jurisdictional youth laws, child placement, and family mediation in the defined geographical area the SDL is responsible for. It may provide expertise in a judiciary setting on child custody, adoption and biological history research.
   */
  UnderageProtectionCenter: v3_RoleCode_UnderageProtectionCenter,
  /**
   * Urology clinic
   */
  UrologyClinic: v3_RoleCode_UrologyClinic,
  /**
   * A specimen used for periodic calibration checks of instruments
   */
  Verifying: v3_RoleCode_Verifying,
  /**
   * An entity authorized to validate information for inclusion in a record.
   */
  Validator: v3_RoleCode_Validator,
  /**
   * Description: A person playing the role of program eligible under a program based on status as a military veteran.
   *
   *
   *                            Discussion: This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is either "program eligible" or "subscriber" and the persons status as a veteran meets jurisdictional or program criteria.
   */
  Veteran: v3_RoleCode_Veteran,
  /**
   * Cattle raised for veal meat production.  Implicit is the husbandry method.
   */
  Veal: v3_RoleCode_Veal,
  /**
   * Description: A person, who is a minor or is deemed incompetent, who plays the role of a program eligible where eligibility for coverage is based on meeting program eligibility criteria for status as a ward of a court or jurisdiction.
   *
   *
   *                            Discussion: This CoveredPartyRoleType.code is typically used when the CoveredPartyRole class code is a "claimant", "program eligible", a "named insured", an "individual Insured" or a "dependent", and the person's status as a ward meets program or policy criteria. In the case of a ward covered under a program providing financial or health benefits, a governmental agency may take temporary custody of a minor or incompetent for his/her protection and care, e.g., if the ward is suffering from neglect or abuse, or has been in trouble with the law.
   */
  Ward: v3_RoleCode_Ward,
  /**
   * The player of the role is a woman joined to a man (scoping person) in marriage.
   */
  Wife: v3_RoleCode_Wife,
  /**
   * Sheep, goats and other mammals raised for their fiber
   */
  Wool: v3_RoleCode_Wool,
  /**
   * Wound clinic
   */
  WoundClinic: v3_RoleCode_WoundClinic,
  /**
   * Animals used to perform work
   */
  Working: v3_RoleCode_Working,
  /**
   * Location where healthcare service was delivered, identified as a work place.
   */
  WorkSite: v3_RoleCode_WorkSite,
};
const v3_RoleLinkStatus_Active: Coding = {
  code: 'ACTIVE',
  display: 'active',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkStatus',
};
const v3_RoleLinkStatus_Cancelled: Coding = {
  code: 'CANCELLED',
  display: 'cancelled',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkStatus',
};
const v3_RoleLinkStatus_Completed: Coding = {
  code: 'COMPLETED',
  display: 'completed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkStatus',
};
const v3_RoleLinkStatus_Normal: Coding = {
  code: 'NORMAL',
  display: 'normal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkStatus',
};
const v3_RoleLinkStatus_Nullified: Coding = {
  code: 'NULLIFIED',
  display: 'nullified',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkStatus',
};
const v3_RoleLinkStatus_Pending: Coding = {
  code: 'PENDING',
  display: 'pending',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkStatus',
};
/**
 *   Description:
 * Codes representing possible states of a RoleLink, as defined by the RoleLink class state machine.
 */
export const V3RoleLinkStatus = {
  /**
   * Description: The state indicates the RoleLink is in progress.
   */
  Active: v3_RoleLinkStatus_Active,
  /**
   * Description: The terminal state resulting from abandoning the RoleLink prior to or after activation.
   */
  Cancelled: v3_RoleLinkStatus_Cancelled,
  /**
   * Description: The terminal state representing the successful completion of the RoleLink.
   */
  Completed: v3_RoleLinkStatus_Completed,
  /**
   * Description: The 'typical' state. Excludes "nullified" which represents the termination state of a RoleLink instance that was created in error.
   */
  Normal: v3_RoleLinkStatus_Normal,
  /**
   * Description: The state representing the termination of a RoleLink instance that was created in error.
   */
  Nullified: v3_RoleLinkStatus_Nullified,
  /**
   * Description: The state indicates the RoleLink has not yet become active.
   */
  Pending: v3_RoleLinkStatus_Pending,
};
const v3_RoleLinkType_IsBackupFor: Coding = {
  code: 'BACKUP',
  display: 'is backup for',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkType',
};
const v3_RoleLinkType_HasContact: Coding = {
  code: 'CONT',
  display: 'has contact',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkType',
};
const v3_RoleLinkType_HasDirectAuthorityOver: Coding = {
  code: 'DIRAUTH',
  display: 'has direct authority over',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkType',
};
const v3_RoleLinkType_Identification: Coding = {
  code: 'IDENT',
  display: 'Identification',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkType',
};
const v3_RoleLinkType_HasIndirectAuthorityOver: Coding = {
  code: 'INDAUTH',
  display: 'has indirect authority over',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkType',
};
const v3_RoleLinkType_HasPart: Coding = {
  code: 'PART',
  display: 'has part',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkType',
};
const v3_RoleLinkType_Related: Coding = {
  code: 'REL',
  display: 'related',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkType',
};
const v3_RoleLinkType_Replaces: Coding = {
  code: 'REPL',
  display: 'replaces',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleLinkType',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3RoleLinkType = {
  /**
   * This relationship indicates the source Role is available to the target Role as a backup. An entity in a backup role will be available as a substitute or replacement in the event that the entity assigned the role is unavailable. In medical roles where it is critical that the function be performed and there is a possibility that the individual assigned may be ill or otherwise indisposed, another individual is assigned to cover for the individual originally assigned the role. A backup may be required to be identified, but unless the backup is actually used, he/she would not assume the assigned entity role.
   */
  IsBackupFor: v3_RoleLinkType_IsBackupFor,
  /**
   * This relationship indicates the target Role provides or receives information regarding the target role.  For example, AssignedEntity is a contact for a ServiceDeliveryLocation.
   */
  HasContact: v3_RoleLinkType_HasContact,
  /**
   * The source Role has direct authority over the target role in a chain of authority.
   */
  HasDirectAuthorityOver: v3_RoleLinkType_HasDirectAuthorityOver,
  /**
   * Description: The source role provides identification for the target role. The source role must be IDENT. The player entity of the source role is constrained to be the same as (i.e. the equivalent of, or equal to) the player of the target role if present. If the player is absent from the source role, then it is assumed to be the same as the player of the target role.
   */
  Identification: v3_RoleLinkType_Identification,
  /**
   * The source Role has indirect authority over the target role in a chain of authority.
   */
  HasIndirectAuthorityOver: v3_RoleLinkType_HasIndirectAuthorityOver,
  /**
   * The target Role is part of the Source Role.
   */
  HasPart: v3_RoleLinkType_HasPart,
  /**
   * An action taken with respect to a subject Entity by a regulatory or authoritative body with supervisory capacity over that entity. The action is taken in response to behavior by the subject Entity that body finds to be undesirable.
   *
   *                         Suspension, license restrictions, monetary fine, letter of reprimand, mandated training, mandated supervision, etc.Examples:
   */
  Related: v3_RoleLinkType_Related,
  /**
   * This relationship indicates that the source Role replaces (or subsumes) the target Role.  Allows for new identifiers and/or new effective time for a registry entry or a certification, etc.
   */
  Replaces: v3_RoleLinkType_Replaces,
};
const v3_RoleStatus_Active: Coding = {
  code: 'active',
  display: 'active',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleStatus',
};
const v3_RoleStatus_Cancelled: Coding = {
  code: 'cancelled',
  display: 'cancelled',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleStatus',
};
const v3_RoleStatus_Normal: Coding = {
  code: 'normal',
  display: 'normal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleStatus',
};
const v3_RoleStatus_Nullified: Coding = {
  code: 'nullified',
  display: 'nullified',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleStatus',
};
const v3_RoleStatus_Pending: Coding = {
  code: 'pending',
  display: 'pending',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleStatus',
};
const v3_RoleStatus_Suspended: Coding = {
  code: 'suspended',
  display: 'suspended',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleStatus',
};
const v3_RoleStatus_Terminated: Coding = {
  code: 'terminated',
  display: 'terminated',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RoleStatus',
};
/**
 *  Codes representing the defined possible states of an Role, as defined by the Role class state machine.
 */
export const V3RoleStatus = {
  /**
   * The state representing the fact that the Entity is currently active in the Role.
   */
  Active: v3_RoleStatus_Active,
  /**
   * The terminal state resulting from cancellation of the role prior to activation.
   */
  Cancelled: v3_RoleStatus_Cancelled,
  /**
   * The 'typical' state. Excludes "nullified" which represents the termination state of a Role instance that was created in error.
   */
  Normal: v3_RoleStatus_Normal,
  /**
   * The state representing the termination of a Role instance that was created in error.
   */
  Nullified: v3_RoleStatus_Nullified,
  /**
   * The state representing that fact that the role has not yet become active.
   */
  Pending: v3_RoleStatus_Pending,
  /**
   * The state that represents a suspension of the Entity playing the Role. This state is arrived at from the "active" state.
   */
  Suspended: v3_RoleStatus_Suspended,
  /**
   * The state representing the successful termination of the Role.
   */
  Terminated: v3_RoleStatus_Terminated,
};
const v3_RouteOfAdministration_AmnioticFluidSacRoute: Coding = {
  code: '_AmnioticFluidSacRoute',
  display: 'AmnioticFluidSacRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_BiliaryRoute: Coding = {
  code: '_BiliaryRoute',
  display: 'BiliaryRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_BodySurfaceRoute: Coding = {
  code: '_BodySurfaceRoute',
  display: 'BodySurfaceRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_BuccalMucosaRoute: Coding = {
  code: '_BuccalMucosaRoute',
  display: 'BuccalMucosaRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_CecostomyRoute: Coding = {
  code: '_CecostomyRoute',
  display: 'CecostomyRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_CervicalRoute: Coding = {
  code: '_CervicalRoute',
  display: 'CervicalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Chew: Coding = {
  code: '_Chew',
  display: 'Chew',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Diffusion: Coding = {
  code: '_Diffusion',
  display: 'Diffusion',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Dissolve: Coding = {
  code: '_Dissolve',
  display: 'Dissolve',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Douche: Coding = {
  code: '_Douche',
  display: 'Douche',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_ElectroOsmosisRoute: Coding = {
  code: '_ElectroOsmosisRoute',
  display: 'ElectroOsmosisRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_EndocervicalRoute: Coding = {
  code: '_EndocervicalRoute',
  display: 'EndocervicalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Enema: Coding = {
  code: '_Enema',
  display: 'Enema',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_EnteralRoute: Coding = {
  code: '_EnteralRoute',
  display: 'EnteralRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_EpiduralRoute: Coding = {
  code: '_EpiduralRoute',
  display: 'EpiduralRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_ExtraAmnioticRoute: Coding = {
  code: '_ExtraAmnioticRoute',
  display: 'ExtraAmnioticRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_ExtracorporealCirculationRoute: Coding = {
  code: '_ExtracorporealCirculationRoute',
  display: 'ExtracorporealCirculationRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Flush: Coding = {
  code: '_Flush',
  display: 'Flush',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_GastricRoute: Coding = {
  code: '_GastricRoute',
  display: 'GastricRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_GenitourinaryRoute: Coding = {
  code: '_GenitourinaryRoute',
  display: 'GenitourinaryRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_GingivalRoute: Coding = {
  code: '_GingivalRoute',
  display: 'GingivalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_HairRoute: Coding = {
  code: '_HairRoute',
  display: 'HairRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Implantation: Coding = {
  code: '_Implantation',
  display: 'Implantation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Infusion: Coding = {
  code: '_Infusion',
  display: 'Infusion',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Inhalation: Coding = {
  code: '_Inhalation',
  display: 'Inhalation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Injection: Coding = {
  code: '_Injection',
  display: 'Injection',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Insertion: Coding = {
  code: '_Insertion',
  display: 'Insertion',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Instillation: Coding = {
  code: '_Instillation',
  display: 'Instillation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InterameningealRoute: Coding = {
  code: '_InterameningealRoute',
  display: 'InterameningealRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InterstitialRoute: Coding = {
  code: '_InterstitialRoute',
  display: 'InterstitialRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraabdominalRoute: Coding = {
  code: '_IntraabdominalRoute',
  display: 'IntraabdominalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraarterialRoute: Coding = {
  code: '_IntraarterialRoute',
  display: 'IntraarterialRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraarticularRoute: Coding = {
  code: '_IntraarticularRoute',
  display: 'IntraarticularRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntrabronchialRoute: Coding = {
  code: '_IntrabronchialRoute',
  display: 'IntrabronchialRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntrabursalRoute: Coding = {
  code: '_IntrabursalRoute',
  display: 'IntrabursalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntracardiacRoute: Coding = {
  code: '_IntracardiacRoute',
  display: 'IntracardiacRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntracartilaginousRoute: Coding = {
  code: '_IntracartilaginousRoute',
  display: 'IntracartilaginousRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntracaudalRoute: Coding = {
  code: '_IntracaudalRoute',
  display: 'IntracaudalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntracavernosalRoute: Coding = {
  code: '_IntracavernosalRoute',
  display: 'IntracavernosalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntracavitaryRoute: Coding = {
  code: '_IntracavitaryRoute',
  display: 'IntracavitaryRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntracerebralRoute: Coding = {
  code: '_IntracerebralRoute',
  display: 'IntracerebralRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntracervicalRoute: Coding = {
  code: '_IntracervicalRoute',
  display: 'IntracervicalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntracisternalRoute: Coding = {
  code: '_IntracisternalRoute',
  display: 'IntracisternalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntracornealRoute: Coding = {
  code: '_IntracornealRoute',
  display: 'IntracornealRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntracoronalRoute: Coding = {
  code: '_IntracoronalRoute',
  display: 'IntracoronalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntracoronaryRoute: Coding = {
  code: '_IntracoronaryRoute',
  display: 'IntracoronaryRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntracorpusCavernosumRoute: Coding = {
  code: '_IntracorpusCavernosumRoute',
  display: 'IntracorpusCavernosumRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntradermalRoute: Coding = {
  code: '_IntradermalRoute',
  display: 'IntradermalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntradiscalRoute: Coding = {
  code: '_IntradiscalRoute',
  display: 'IntradiscalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraductalRoute: Coding = {
  code: '_IntraductalRoute',
  display: 'IntraductalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraduodenalRoute: Coding = {
  code: '_IntraduodenalRoute',
  display: 'IntraduodenalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraduralRoute: Coding = {
  code: '_IntraduralRoute',
  display: 'IntraduralRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraepidermalRoute: Coding = {
  code: '_IntraepidermalRoute',
  display: 'IntraepidermalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraepithelialRoute: Coding = {
  code: '_IntraepithelialRoute',
  display: 'IntraepithelialRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraesophagealRoute: Coding = {
  code: '_IntraesophagealRoute',
  display: 'IntraesophagealRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntragastricRoute: Coding = {
  code: '_IntragastricRoute',
  display: 'IntragastricRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntrailealRoute: Coding = {
  code: '_IntrailealRoute',
  display: 'IntrailealRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntralesionalRoute: Coding = {
  code: '_IntralesionalRoute',
  display: 'IntralesionalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraluminalRoute: Coding = {
  code: '_IntraluminalRoute',
  display: 'IntraluminalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntralymphaticRoute: Coding = {
  code: '_IntralymphaticRoute',
  display: 'IntralymphaticRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntramedullaryRoute: Coding = {
  code: '_IntramedullaryRoute',
  display: 'IntramedullaryRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntramuscularRoute: Coding = {
  code: '_IntramuscularRoute',
  display: 'IntramuscularRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraocularRoute: Coding = {
  code: '_IntraocularRoute',
  display: 'IntraocularRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraosseousRoute: Coding = {
  code: '_IntraosseousRoute',
  display: 'IntraosseousRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraovarianRoute: Coding = {
  code: '_IntraovarianRoute',
  display: 'IntraovarianRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntrapericardialRoute: Coding = {
  code: '_IntrapericardialRoute',
  display: 'IntrapericardialRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraperitonealRoute: Coding = {
  code: '_IntraperitonealRoute',
  display: 'IntraperitonealRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntrapleuralRoute: Coding = {
  code: '_IntrapleuralRoute',
  display: 'IntrapleuralRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraprostaticRoute: Coding = {
  code: '_IntraprostaticRoute',
  display: 'IntraprostaticRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntrapulmonaryRoute: Coding = {
  code: '_IntrapulmonaryRoute',
  display: 'IntrapulmonaryRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntrasinalRoute: Coding = {
  code: '_IntrasinalRoute',
  display: 'IntrasinalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraspinalRoute: Coding = {
  code: '_IntraspinalRoute',
  display: 'IntraspinalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntrasternalRoute: Coding = {
  code: '_IntrasternalRoute',
  display: 'IntrasternalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntrasynovialRoute: Coding = {
  code: '_IntrasynovialRoute',
  display: 'IntrasynovialRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntratendinousRoute: Coding = {
  code: '_IntratendinousRoute',
  display: 'IntratendinousRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntratesticularRoute: Coding = {
  code: '_IntratesticularRoute',
  display: 'IntratesticularRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntrathecalRoute: Coding = {
  code: '_IntrathecalRoute',
  display: 'IntrathecalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntrathoracicRoute: Coding = {
  code: '_IntrathoracicRoute',
  display: 'IntrathoracicRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntratrachealRoute: Coding = {
  code: '_IntratrachealRoute',
  display: 'IntratrachealRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntratubularRoute: Coding = {
  code: '_IntratubularRoute',
  display: 'IntratubularRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntratumorRoute: Coding = {
  code: '_IntratumorRoute',
  display: 'IntratumorRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntratympanicRoute: Coding = {
  code: '_IntratympanicRoute',
  display: 'IntratympanicRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntrauterineRoute: Coding = {
  code: '_IntrauterineRoute',
  display: 'IntrauterineRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntravascularRoute: Coding = {
  code: '_IntravascularRoute',
  display: 'IntravascularRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntravenousRoute: Coding = {
  code: '_IntravenousRoute',
  display: 'IntravenousRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntraventricularRoute: Coding = {
  code: '_IntraventricularRoute',
  display: 'IntraventricularRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntravesicleRoute: Coding = {
  code: '_IntravesicleRoute',
  display: 'IntravesicleRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IntravitrealRoute: Coding = {
  code: '_IntravitrealRoute',
  display: 'IntravitrealRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IontophoresisRoute: Coding = {
  code: '_IontophoresisRoute',
  display: 'IontophoresisRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Irrigation: Coding = {
  code: '_Irrigation',
  display: 'Irrigation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_JejunumRoute: Coding = {
  code: '_JejunumRoute',
  display: 'JejunumRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_LacrimalPunctaRoute: Coding = {
  code: '_LacrimalPunctaRoute',
  display: 'LacrimalPunctaRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_LaryngealRoute: Coding = {
  code: '_LaryngealRoute',
  display: 'LaryngealRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_LavageRoute: Coding = {
  code: '_LavageRoute',
  display: 'LavageRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_LingualRoute: Coding = {
  code: '_LingualRoute',
  display: 'LingualRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_MucosalAbsorptionRoute: Coding = {
  code: '_MucosalAbsorptionRoute',
  display: 'MucosalAbsorptionRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_MucousMembraneRoute: Coding = {
  code: '_MucousMembraneRoute',
  display: 'MucousMembraneRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_NailRoute: Coding = {
  code: '_NailRoute',
  display: 'NailRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_NasalRoute: Coding = {
  code: '_NasalRoute',
  display: 'NasalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Nebulization: Coding = {
  code: '_Nebulization',
  display: 'Nebulization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_OphthalmicRoute: Coding = {
  code: '_OphthalmicRoute',
  display: 'OphthalmicRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_OralRoute: Coding = {
  code: '_OralRoute',
  display: 'OralRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_OromucosalRoute: Coding = {
  code: '_OromucosalRoute',
  display: 'OromucosalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_OropharyngealRoute: Coding = {
  code: '_OropharyngealRoute',
  display: 'OropharyngealRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_OticRoute: Coding = {
  code: '_OticRoute',
  display: 'OticRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_ParanasalSinusesRoute: Coding = {
  code: '_ParanasalSinusesRoute',
  display: 'ParanasalSinusesRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_ParenteralRoute: Coding = {
  code: '_ParenteralRoute',
  display: 'ParenteralRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_PerianalRoute: Coding = {
  code: '_PerianalRoute',
  display: 'PerianalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_PeriarticularRoute: Coding = {
  code: '_PeriarticularRoute',
  display: 'PeriarticularRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_PeriduralRoute: Coding = {
  code: '_PeriduralRoute',
  display: 'PeriduralRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_PerinealRoute: Coding = {
  code: '_PerinealRoute',
  display: 'PerinealRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_PerineuralRoute: Coding = {
  code: '_PerineuralRoute',
  display: 'PerineuralRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_PeriodontalRoute: Coding = {
  code: '_PeriodontalRoute',
  display: 'PeriodontalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_PulmonaryRoute: Coding = {
  code: '_PulmonaryRoute',
  display: 'PulmonaryRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_RectalRoute: Coding = {
  code: '_RectalRoute',
  display: 'RectalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_RespiratoryTractRoute: Coding = {
  code: '_RespiratoryTractRoute',
  display: 'RespiratoryTractRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_RetrobulbarRoute: Coding = {
  code: '_RetrobulbarRoute',
  display: 'RetrobulbarRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Rinse: Coding = {
  code: '_Rinse',
  display: 'Rinse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_RouteByMethod: Coding = {
  code: '_RouteByMethod',
  display: 'RouteByMethod',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_RouteBySite: Coding = {
  code: '_RouteBySite',
  display: 'RouteBySite',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_ScalpRoute: Coding = {
  code: '_ScalpRoute',
  display: 'ScalpRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SinusUnspecifiedRoute: Coding = {
  code: '_SinusUnspecifiedRoute',
  display: 'SinusUnspecifiedRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SkinRoute: Coding = {
  code: '_SkinRoute',
  display: 'SkinRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SoftTissueRoute: Coding = {
  code: '_SoftTissueRoute',
  display: 'SoftTissueRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SubarachnoidRoute: Coding = {
  code: '_SubarachnoidRoute',
  display: 'SubarachnoidRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SubconjunctivalRoute: Coding = {
  code: '_SubconjunctivalRoute',
  display: 'SubconjunctivalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SubcutaneousRoute: Coding = {
  code: '_SubcutaneousRoute',
  display: 'SubcutaneousRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SublesionalRoute: Coding = {
  code: '_SublesionalRoute',
  display: 'SublesionalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SublingualRoute: Coding = {
  code: '_SublingualRoute',
  display: 'SublingualRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SubmucosalRoute: Coding = {
  code: '_SubmucosalRoute',
  display: 'SubmucosalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SuppositoryRoute: Coding = {
  code: '_SuppositoryRoute',
  display: 'SuppositoryRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Swish: Coding = {
  code: '_Swish',
  display: 'Swish',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalAbsorptionRoute: Coding = {
  code: '_TopicalAbsorptionRoute',
  display: 'TopicalAbsorptionRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplication: Coding = {
  code: '_TopicalApplication',
  display: 'TopicalApplication',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TracheostomyRoute: Coding = {
  code: '_TracheostomyRoute',
  display: 'TracheostomyRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TransmucosalRoute: Coding = {
  code: '_TransmucosalRoute',
  display: 'TransmucosalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TransplacentalRoute: Coding = {
  code: '_TransplacentalRoute',
  display: 'TransplacentalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TranstrachealRoute: Coding = {
  code: '_TranstrachealRoute',
  display: 'TranstrachealRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TranstympanicRoute: Coding = {
  code: '_TranstympanicRoute',
  display: 'TranstympanicRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_UreteralRoute: Coding = {
  code: '_UreteralRoute',
  display: 'UreteralRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_UrethralRoute: Coding = {
  code: '_UrethralRoute',
  display: 'UrethralRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_UrinaryBladderRoute: Coding = {
  code: '_UrinaryBladderRoute',
  display: 'UrinaryBladderRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_UrinaryTractRoute: Coding = {
  code: '_UrinaryTractRoute',
  display: 'UrinaryTractRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_VaginalRoute: Coding = {
  code: '_VaginalRoute',
  display: 'VaginalRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_VitreousHumourRoute: Coding = {
  code: '_VitreousHumourRoute',
  display: 'VitreousHumourRoute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionAmnioticFluid: Coding = {
  code: 'AMNINJ',
  display: 'Injection, amniotic fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionBiliaryTract: Coding = {
  code: 'BILINJ',
  display: 'Injection, biliary tract',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionUrinaryBladder: Coding = {
  code: 'BLADINJ',
  display: 'Injection, urinary bladder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationUrinaryCatheter: Coding = {
  code: 'BLADINSTL',
  display: 'Instillation, urinary catheter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IrrigationUrinaryBladder: Coding = {
  code: 'BLADIRR',
  display: 'Irrigation, urinary bladder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IrrigationUrinaryBladderContinuous: Coding = {
  code: 'BLADIRRC',
  display: 'Irrigation, urinary bladder, continuous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IrrigationUrinaryBladderTidal: Coding = {
  code: 'BLADIRRT',
  display: 'Irrigation, urinary bladder, tidal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationBuccal: Coding = {
  code: 'BUC',
  display: 'Topical application, buccal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationContinuousAmbulatoryPeritonealDialysisPort: Coding = {
  code: 'CAPDINSTL',
  display: 'Instillation, continuous ambulatory peritoneal dialysis port',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationCecostomy: Coding = {
  code: 'CECINSTL',
  display: 'Instillation, cecostomy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationCervical: Coding = {
  code: 'CERV',
  display: 'Topical application, cervical',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionCervical: Coding = {
  code: 'CERVINJ',
  display: 'Injection, cervical',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InsertionCervicalUterine: Coding = {
  code: 'CERVINS',
  display: 'Insertion, cervical (uterine)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_ChewOral: Coding = {
  code: 'CHEW',
  display: 'Chew, oral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionForCholangiography: Coding = {
  code: 'CHOLINJ',
  display: 'Injection, for cholangiography',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationChestTube: Coding = {
  code: 'CTINSTL',
  display: 'Instillation, chest tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationDental: Coding = {
  code: 'DEN',
  display: 'Topical application, dental',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_RinseDental: Coding = {
  code: 'DENRINSE',
  display: 'Rinse, dental',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_DissolveOral: Coding = {
  code: 'DISSOLVE',
  display: 'Dissolve, oral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_DoucheVaginal: Coding = {
  code: 'DOUCHE',
  display: 'Douche, vaginal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationSoakedDressing: Coding = {
  code: 'DRESS',
  display: 'Topical application, soaked dressing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_ElectroOsmosis: Coding = {
  code: 'ELECTOSMOS',
  display: 'Electro-osmosis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionEndosinusial: Coding = {
  code: 'ENDOSININJ',
  display: 'Injection, endosinusial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_EnemaRectal: Coding = {
  code: 'ENEMA',
  display: 'Enema, rectal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InfusionEpidural: Coding = {
  code: 'EPI',
  display: 'Infusion, epidural',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionEpidural: Coding = {
  code: 'EPIDURINJ',
  display: 'Injection, epidural',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionEpiduralPush: Coding = {
  code: 'EPIINJ',
  display: 'Injection, epidural, push',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionEpiduralSlowPush: Coding = {
  code: 'EPINJSP',
  display: 'Injection, epidural, slow push',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationEndotrachealTube: Coding = {
  code: 'ETINSTL',
  display: 'Instillation, endotracheal tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_NebulizationEndotrachealTube: Coding = {
  code: 'ETNEB',
  display: 'Nebulization, endotracheal tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_DiffusionExtracorporeal: Coding = {
  code: 'EXTCORPDIF',
  display: 'Diffusion, extracorporeal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionExtracorporeal: Coding = {
  code: 'EXTCORPINJ',
  display: 'Injection, extracorporeal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionExtraAmniotic: Coding = {
  code: 'EXTRAMNINJ',
  display: 'Injection, extra-amniotic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Gargle: Coding = {
  code: 'GARGLE',
  display: 'Gargle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionGastricButton: Coding = {
  code: 'GBINJ',
  display: 'Injection, gastric button',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationGingival: Coding = {
  code: 'GIN',
  display: 'Topical application, gingival',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionGingival: Coding = {
  code: 'GINGINJ',
  display: 'Injection, gingival',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IrrigationGenitourinary: Coding = {
  code: 'GUIRR',
  display: 'Irrigation, genitourinary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationHair: Coding = {
  code: 'HAIR',
  display: 'Topical application, hair',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_DiffusionHemodialysis: Coding = {
  code: 'HEMODIFF',
  display: 'Diffusion, hemodialysis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionHemodialysisPort: Coding = {
  code: 'HEMOPORT',
  display: 'Injection, hemodialysis port',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InfusionIntraarterialCatheter: Coding = {
  code: 'IA',
  display: 'Infusion, intraarterial catheter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraAbdominal: Coding = {
  code: 'IABDINJ',
  display: 'Injection, intra-abdominal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraarterial: Coding = {
  code: 'IAINJ',
  display: 'Injection, intraarterial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraarterialPush: Coding = {
  code: 'IAINJP',
  display: 'Injection, intraarterial, push',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraarterialSlowPush: Coding = {
  code: 'IAINJSP',
  display: 'Injection, intraarterial, slow push',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraarticular: Coding = {
  code: 'IARTINJ',
  display: 'Injection, intraarticular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationIntrabronchial: Coding = {
  code: 'IBRONCHINSTIL',
  display: 'Instillation, intrabronchial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntrabursal: Coding = {
  code: 'IBURSINJ',
  display: 'Injection, intrabursal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InfusionIntracardiac: Coding = {
  code: 'IC',
  display: 'Infusion, intracardiac',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracardiac: Coding = {
  code: 'ICARDINJ',
  display: 'Injection, intracardiac',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracardiacRapidPush: Coding = {
  code: 'ICARDINJRP',
  display: 'Injection, intracardiac, rapid push',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracardiacSlowPush: Coding = {
  code: 'ICARDINJSP',
  display: 'Injection, intracardiac, slow push',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracardiacPush: Coding = {
  code: 'ICARINJP',
  display: 'Injection, intracardiac, push',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracartilaginous: Coding = {
  code: 'ICARTINJ',
  display: 'Injection, intracartilaginous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracaudal: Coding = {
  code: 'ICAUDINJ',
  display: 'Injection, intracaudal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracavernous: Coding = {
  code: 'ICAVINJ',
  display: 'Injection, intracavernous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracavitary: Coding = {
  code: 'ICAVITINJ',
  display: 'Injection, intracavitary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracerebral: Coding = {
  code: 'ICEREBINJ',
  display: 'Injection, intracerebral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracisternal: Coding = {
  code: 'ICISTERNINJ',
  display: 'Injection, intracisternal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InfusionIntracoronary: Coding = {
  code: 'ICOR',
  display: 'Infusion, intracoronary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationIntracorneal: Coding = {
  code: 'ICORNTA',
  display: 'Topical application, intracorneal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracoronary: Coding = {
  code: 'ICORONINJ',
  display: 'Injection, intracoronary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracoronaryPush: Coding = {
  code: 'ICORONINJP',
  display: 'Injection, intracoronary, push',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationIntracoronalDental: Coding = {
  code: 'ICORONTA',
  display: 'Topical application, intracoronal (dental)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracorpusCavernosum: Coding = {
  code: 'ICORPCAVINJ',
  display: 'Injection, intracorpus cavernosum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_ImplantationIntradermal: Coding = {
  code: 'IDIMPLNT',
  display: 'Implantation, intradermal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntradiscal: Coding = {
  code: 'IDISCINJ',
  display: 'Injection, intradiscal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_MucosalAbsorptionIntraduodenal: Coding = {
  code: 'IDOUDMAB',
  display: 'Mucosal absorption, intraduodenal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraductal: Coding = {
  code: 'IDUCTINJ',
  display: 'Injection, intraductal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationIntraduodenal: Coding = {
  code: 'IDUODINSTIL',
  display: 'Instillation, intraduodenal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntradural: Coding = {
  code: 'IDURINJ',
  display: 'Injection, intradural',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraepidermal: Coding = {
  code: 'IEPIDINJ',
  display: 'Injection, intraepidermal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraepithelial: Coding = {
  code: 'IEPITHINJ',
  display: 'Injection, intraepithelial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationIntraesophageal: Coding = {
  code: 'IESOPHINSTIL',
  display: 'Instillation, intraesophageal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationIntraesophageal: Coding = {
  code: 'IESOPHTA',
  display: 'Topical application, intraesophageal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationIntragastric: Coding = {
  code: 'IGASTINSTIL',
  display: 'Instillation, intragastric',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IrrigationIntragastric: Coding = {
  code: 'IGASTIRR',
  display: 'Irrigation, intragastric',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_LavageIntragastric: Coding = {
  code: 'IGASTLAV',
  display: 'Lavage, intragastric',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationIntraileal: Coding = {
  code: 'IILEALINJ',
  display: 'Instillation, intraileal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationIntraileal: Coding = {
  code: 'IILEALTA',
  display: 'Topical application, intraileal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntralesional: Coding = {
  code: 'ILESINJ',
  display: 'Injection, intralesional',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IrrigationIntralesional: Coding = {
  code: 'ILESIRR',
  display: 'Irrigation, intralesional',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationIntralesional: Coding = {
  code: 'ILTOP',
  display: 'Topical application, intralesional',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraluminal: Coding = {
  code: 'ILUMINJ',
  display: 'Injection, intraluminal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationIntraluminal: Coding = {
  code: 'ILUMTA',
  display: 'Topical application, intraluminal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntralymphatic: Coding = {
  code: 'ILYMPJINJ',
  display: 'Injection, intralymphatic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntramuscularDeep: Coding = {
  code: 'IMD',
  display: 'Injection, intramuscular, deep',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntramedullary: Coding = {
  code: 'IMEDULINJ',
  display: 'Injection, intramedullary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntramuscularZTrack: Coding = {
  code: 'IMZ',
  display: 'Injection, intramuscular, z track',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Insufflation: Coding = {
  code: 'INSUF',
  display: 'Insufflation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionInterameningeal: Coding = {
  code: 'INTERMENINJ',
  display: 'Injection, interameningeal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionInterstitial: Coding = {
  code: 'INTERSTITINJ',
  display: 'Injection, interstitial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraocular: Coding = {
  code: 'IOINJ',
  display: 'Injection, intraocular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationIntraocular: Coding = {
  code: 'IOINSTL',
  display: 'Instillation, intraocular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IrrigationIntraocular: Coding = {
  code: 'IOIRR',
  display: 'Irrigation, intraocular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationIontophoresis: Coding = {
  code: 'IONTO',
  display: 'Topical application, iontophoresis',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InfusionIntraosseousContinuous: Coding = {
  code: 'IOSSC',
  display: 'Infusion, intraosseous, continuous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraosseous: Coding = {
  code: 'IOSSINJ',
  display: 'Injection, intraosseous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InsertionIntraocularSurgical: Coding = {
  code: 'IOSURGINS',
  display: 'Insertion, intraocular, surgical',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationIntraocular: Coding = {
  code: 'IOTOP',
  display: 'Topical application, intraocular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraovarian: Coding = {
  code: 'IOVARINJ',
  display: 'Injection, intraovarian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntrapericardial: Coding = {
  code: 'IPCARDINJ',
  display: 'Injection, intrapericardial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraperitoneal: Coding = {
  code: 'IPERINJ',
  display: 'Injection, intraperitoneal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InhalationRespiratory: Coding = {
  code: 'IPINHL',
  display: 'Inhalation, respiratory',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntrapulmonary: Coding = {
  code: 'IPINJ',
  display: 'Injection, intrapulmonary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntrapleural: Coding = {
  code: 'IPLRINJ',
  display: 'Injection, intrapleural',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InhalationIntermittentPositivePressureBreathingIppb: Coding = {
  code: 'IPPB',
  display: 'Inhalation, intermittent positive pressure breathing (ippb)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraprostatic: Coding = {
  code: 'IPROSTINJ',
  display: 'Injection, intraprostatic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionInsulinPump: Coding = {
  code: 'IPUMPINJ',
  display: 'Injection, insulin pump',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationIntrasinal: Coding = {
  code: 'ISININSTIL',
  display: 'Instillation, intrasinal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraspinal: Coding = {
  code: 'ISINJ',
  display: 'Injection, intraspinal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntrasternal: Coding = {
  code: 'ISTERINJ',
  display: 'Injection, intrasternal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntrasynovial: Coding = {
  code: 'ISYNINJ',
  display: 'Injection, intrasynovial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InfusionIntrathecal: Coding = {
  code: 'IT',
  display: 'Infusion, intrathecal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntratendinous: Coding = {
  code: 'ITENDINJ',
  display: 'Injection, intratendinous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntratesticular: Coding = {
  code: 'ITESTINJ',
  display: 'Injection, intratesticular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntrathoracic: Coding = {
  code: 'ITHORINJ',
  display: 'Injection, intrathoracic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntrathecal: Coding = {
  code: 'ITINJ',
  display: 'Injection, intrathecal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationIntratracheal: Coding = {
  code: 'ITRACHINSTIL',
  display: 'Instillation, intratracheal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_MucosalAbsorptionIntratracheal: Coding = {
  code: 'ITRACHMAB',
  display: 'Mucosal absorption, intratracheal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntratubular: Coding = {
  code: 'ITUBINJ',
  display: 'Injection, intratubular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntratumor: Coding = {
  code: 'ITUMINJ',
  display: 'Injection, intratumor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntratympanic: Coding = {
  code: 'ITYMPINJ',
  display: 'Injection, intratympanic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InsertionIntrauterine: Coding = {
  code: 'IU',
  display: 'Insertion, intrauterine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntrauterine: Coding = {
  code: 'IUINJ',
  display: 'Injection, intrauterine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntracervicalUterus: Coding = {
  code: 'IUINJC',
  display: 'Injection, intracervical (uterus)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationIntrauterine: Coding = {
  code: 'IUINSTL',
  display: 'Instillation, intrauterine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraureteralRetrograde: Coding = {
  code: 'IURETINJ',
  display: 'Injection, intraureteral, retrograde',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InfusionIntravenous: Coding = {
  code: 'IV',
  display: 'Infusion, intravenous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InfusionIntravascular: Coding = {
  code: 'IVASCINFUS',
  display: 'Infusion, intravascular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntravascular: Coding = {
  code: 'IVASCINJ',
  display: 'Injection, intravascular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InfusionIntravenousCatheter: Coding = {
  code: 'IVC',
  display: 'Infusion, intravenous catheter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InfusionIntravenousCatheterContinuous: Coding = {
  code: 'IVCC',
  display: 'Infusion, intravenous catheter, continuous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InfusionIntravenousCatheterIntermittent: Coding = {
  code: 'IVCI',
  display: 'Infusion, intravenous catheter, intermittent',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntraventricularHeart: Coding = {
  code: 'IVENTINJ',
  display: 'Injection, intraventricular (heart)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntravesicle: Coding = {
  code: 'IVESINJ',
  display: 'Injection, intravesicle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_FlushIntravenousCatheter: Coding = {
  code: 'IVFLUSH',
  display: 'Flush, intravenous catheter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntravenousBolus: Coding = {
  code: 'IVINJBOL',
  display: 'Injection, intravenous, bolus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_ImplantationIntravitreal: Coding = {
  code: 'IVITIMPLNT',
  display: 'Implantation, intravitreal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntravitreal: Coding = {
  code: 'IVITINJ',
  display: 'Injection, intravitreal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntravenousPush: Coding = {
  code: 'IVPUSH',
  display: 'Injection, intravenous, push',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntravenousRapidPush: Coding = {
  code: 'IVRPUSH',
  display: 'Injection, intravenous, rapid push',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionIntravenousSlowPush: Coding = {
  code: 'IVSPUSH',
  display: 'Injection, intravenous, slow push',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationLaryngeal: Coding = {
  code: 'LARYNGINSTIL',
  display: 'Instillation, laryngeal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationLaryngeal: Coding = {
  code: 'LARYNGTA',
  display: 'Topical application, laryngeal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InsertionLacrimalPuncta: Coding = {
  code: 'LPINS',
  display: 'Insertion, lacrimal puncta',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationMucousMembrane: Coding = {
  code: 'MUC',
  display: 'Topical application, mucous membrane',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationNail: Coding = {
  code: 'NAIL',
  display: 'Topical application, nail',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationNasal: Coding = {
  code: 'NASAL',
  display: 'Topical application, nasal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationNasal: Coding = {
  code: 'NASALINSTIL',
  display: 'Instillation, nasal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InhalationNasal: Coding = {
  code: 'NASINHL',
  display: 'Inhalation, nasal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InhalationNebulizationNasal: Coding = {
  code: 'NASNEB',
  display: 'Inhalation, nebulization, nasal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationNasogastric: Coding = {
  code: 'NASOGASINSTIL',
  display: 'Instillation, nasogastric',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InhalationNebulization: Coding = {
  code: 'NEB',
  display: 'Inhalation, nebulization',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationNasotrachealTube: Coding = {
  code: 'NTT',
  display: 'Instillation, nasotracheal tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_OcclusiveDressingTechnique: Coding = {
  code: 'OCDRESTA',
  display: 'Occlusive dressing technique',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationOphthalmic: Coding = {
  code: 'OPTHALTA',
  display: 'Topical application, ophthalmic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationOral: Coding = {
  code: 'ORALTA',
  display: 'Topical application, oral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InhalationOralIntermittentFlow: Coding = {
  code: 'ORIFINHL',
  display: 'Inhalation, oral intermittent flow',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationOromucosal: Coding = {
  code: 'ORMUC',
  display: 'Topical application, oromucosal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InhalationNebulizationOral: Coding = {
  code: 'ORNEB',
  display: 'Inhalation, nebulization, oral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationOropharyngeal: Coding = {
  code: 'OROPHARTA',
  display: 'Topical application, oropharyngeal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_RinseOral: Coding = {
  code: 'ORRINSE',
  display: 'Rinse, oral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationOtic: Coding = {
  code: 'OT',
  display: 'Instillation, otic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionPeriarticular: Coding = {
  code: 'PAINJ',
  display: 'Injection, periarticular',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionParenteral: Coding = {
  code: 'PARENTINJ',
  display: 'Injection, parenteral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InfusionIntravenousCatheterPcaPump: Coding = {
  code: 'PCA',
  display: 'Infusion, intravenous catheter, pca pump',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionPeriodontal: Coding = {
  code: 'PDONTINJ',
  display: 'Injection, periodontal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationPeriodontal: Coding = {
  code: 'PDONTTA',
  display: 'Topical application, periodontal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionPeritonealDialysisPort: Coding = {
  code: 'PDPINJ',
  display: 'Injection, peritoneal dialysis port',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationPeritonealDialysisPort: Coding = {
  code: 'PDPINSTL',
  display: 'Instillation, peritoneal dialysis port',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionPeridural: Coding = {
  code: 'PDURINJ',
  display: 'Injection, peridural',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationPerianal: Coding = {
  code: 'PERIANAL',
  display: 'Topical application, perianal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationPerineal: Coding = {
  code: 'PERINEAL',
  display: 'Topical application, perineal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionPerineural: Coding = {
  code: 'PNINJ',
  display: 'Injection, perineural',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionParanasalSinuses: Coding = {
  code: 'PNSINJ',
  display: 'Injection, paranasal sinuses',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationParanasalSinuses: Coding = {
  code: 'PNSINSTL',
  display: 'Instillation, paranasal sinuses',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InsertionRectal: Coding = {
  code: 'PR',
  display: 'Insertion, rectal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionRetrobulbar: Coding = {
  code: 'RBINJ',
  display: 'Injection, retrobulbar',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InhalationOralRebreatherMask: Coding = {
  code: 'REBREATH',
  display: 'Inhalation, oral rebreather mask',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationRectal: Coding = {
  code: 'RECINSTL',
  display: 'Instillation, rectal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_IrrigationRectal: Coding = {
  code: 'RECIRR',
  display: 'Irrigation, rectal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationRectal: Coding = {
  code: 'RECTAL',
  display: 'Topical application, rectal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationRectalTube: Coding = {
  code: 'RECTINSTL',
  display: 'Instillation, rectal tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_EnemaRectalRetention: Coding = {
  code: 'RETENEMA',
  display: 'Enema, rectal retention',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationScalp: Coding = {
  code: 'SCALP',
  display: 'Topical application, scalp',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionSubconjunctival: Coding = {
  code: 'SCINJ',
  display: 'Injection, subconjunctival',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Shampoo: Coding = {
  code: 'SHAMPOO',
  display: 'Shampoo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationSinusUnspecified: Coding = {
  code: 'SININSTIL',
  display: 'Instillation, sinus, unspecified',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationSkin: Coding = {
  code: 'SKIN',
  display: 'Topical application, skin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_DissolveSublingual: Coding = {
  code: 'SL',
  display: 'Dissolve, sublingual',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionSublesional: Coding = {
  code: 'SLESINJ',
  display: 'Injection, sublesional',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_MucosalAbsorptionSubmucosal: Coding = {
  code: 'SMUCMAB',
  display: 'Mucosal absorption, submucosal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_ImmersionSoak: Coding = {
  code: 'SOAK',
  display: 'Immersion (soak)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionSoftTissue: Coding = {
  code: 'SOFTISINJ',
  display: 'Injection, soft tissue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationSoftTissue: Coding = {
  code: 'SOFTISINSTIL',
  display: 'Instillation, soft tissue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_ImplantationSubcutaneous: Coding = {
  code: 'SQIMPLNT',
  display: 'Implantation, subcutaneous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InfusionSubcutaneous: Coding = {
  code: 'SQINFUS',
  display: 'Infusion, subcutaneous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InsertionSubcutaneousSurgical: Coding = {
  code: 'SQSURGINS',
  display: 'Insertion, subcutaneous, surgical',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionSubarachnoid: Coding = {
  code: 'SUBARACHINJ',
  display: 'Injection, subarachnoid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Subconjunctival: Coding = {
  code: 'SUBCONJTA',
  display: 'Subconjunctival',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionSubmucosal: Coding = {
  code: 'SUBMUCINJ',
  display: 'Injection, submucosal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SuckOromucosal: Coding = {
  code: 'SUCK',
  display: 'Suck, oromucosal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationSwab: Coding = {
  code: 'SWAB',
  display: 'Topical application, swab',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SwishAndSpitOutOromucosal: Coding = {
  code: 'SWISHSPIT',
  display: 'Swish and spit out, oromucosal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SwishAndSwallowOromucosal: Coding = {
  code: 'SWISHSWAL',
  display: 'Swish and swallow, oromucosal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationTransmucosal: Coding = {
  code: 'TMUCTA',
  display: 'Topical application, transmucosal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Topical: Coding = {
  code: 'TOPICAL',
  display: 'Topical',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InhalationTracheostomy: Coding = {
  code: 'TRACH',
  display: 'Inhalation, tracheostomy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationTracheostomy: Coding = {
  code: 'TRACHINSTL',
  display: 'Instillation, tracheostomy',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_DiffusionTransdermal: Coding = {
  code: 'TRNSDERMD',
  display: 'Diffusion, transdermal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_Translingual: Coding = {
  code: 'TRNSLING',
  display: 'Translingual',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionTransplacental: Coding = {
  code: 'TRPLACINJ',
  display: 'Injection, transplacental',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionTranstracheal: Coding = {
  code: 'TRTRACHINJ',
  display: 'Injection, transtracheal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationTranstympanic: Coding = {
  code: 'TRTYMPINSTIL',
  display: 'Instillation, transtympanic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalAbsorptionTranstympanic: Coding = {
  code: 'TTYMPTABSORP',
  display: 'Topical absorption, transtympanic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionUrethral: Coding = {
  code: 'URETHINJ',
  display: 'Injection, urethral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InsertionUrethral: Coding = {
  code: 'URETHINS',
  display: 'Insertion, urethral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InstillationUrethral: Coding = {
  code: 'URETHINSTL',
  display: 'instillation, urethral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_SuppositoryUrethral: Coding = {
  code: 'URETHSUP',
  display: 'Suppository, urethral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InjectionUreteral: Coding = {
  code: 'URETINJ',
  display: 'Injection, ureteral',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_TopicalApplicationVaginal: Coding = {
  code: 'VAGINS',
  display: 'Topical application, vaginal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InsertionVaginal: Coding = {
  code: 'VAGINSI',
  display: 'Insertion, vaginal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InhalationVentilator: Coding = {
  code: 'VENT',
  display: 'Inhalation, ventilator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
const v3_RouteOfAdministration_InhalationVentimask: Coding = {
  code: 'VENTMASK',
  display: 'Inhalation, ventimask',
  system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
};
/**
 *  The path the administered medication takes to get into the body or into contact with the body.
 */
export const V3RouteOfAdministration = {
  /**
   * Amniotic fluid sac
   */
  AmnioticFluidSacRoute: v3_RouteOfAdministration_AmnioticFluidSacRoute,
  /**
   * Biliary tract
   */
  BiliaryRoute: v3_RouteOfAdministration_BiliaryRoute,
  /**
   * Body surface
   */
  BodySurfaceRoute: v3_RouteOfAdministration_BodySurfaceRoute,
  /**
   * Buccal mucosa
   */
  BuccalMucosaRoute: v3_RouteOfAdministration_BuccalMucosaRoute,
  /**
   * Cecostomy
   */
  CecostomyRoute: v3_RouteOfAdministration_CecostomyRoute,
  /**
   * Cervix of the uterus
   */
  CervicalRoute: v3_RouteOfAdministration_CervicalRoute,
  /**
   * Chew
   */
  Chew: v3_RouteOfAdministration_Chew,
  /**
   * Diffusion
   */
  Diffusion: v3_RouteOfAdministration_Diffusion,
  /**
   * Dissolve
   */
  Dissolve: v3_RouteOfAdministration_Dissolve,
  /**
   * Douche
   */
  Douche: v3_RouteOfAdministration_Douche,
  /**
   * Electro-osmosis
   */
  ElectroOsmosisRoute: v3_RouteOfAdministration_ElectroOsmosisRoute,
  /**
   * Endocervical
   */
  EndocervicalRoute: v3_RouteOfAdministration_EndocervicalRoute,
  /**
   * Enema
   */
  Enema: v3_RouteOfAdministration_Enema,
  /**
   * Enteral
   */
  EnteralRoute: v3_RouteOfAdministration_EnteralRoute,
  /**
   * Epidural
   */
  EpiduralRoute: v3_RouteOfAdministration_EpiduralRoute,
  /**
   * Extra-amniotic
   */
  ExtraAmnioticRoute: v3_RouteOfAdministration_ExtraAmnioticRoute,
  /**
   * Extracorporeal circulation
   */
  ExtracorporealCirculationRoute: v3_RouteOfAdministration_ExtracorporealCirculationRoute,
  /**
   * Flush
   */
  Flush: v3_RouteOfAdministration_Flush,
  /**
   * Gastric
   */
  GastricRoute: v3_RouteOfAdministration_GastricRoute,
  /**
   * Genitourinary
   */
  GenitourinaryRoute: v3_RouteOfAdministration_GenitourinaryRoute,
  /**
   * Gingival
   */
  GingivalRoute: v3_RouteOfAdministration_GingivalRoute,
  /**
   * Hair
   */
  HairRoute: v3_RouteOfAdministration_HairRoute,
  /**
   * Implantation
   */
  Implantation: v3_RouteOfAdministration_Implantation,
  /**
   * Infusion
   */
  Infusion: v3_RouteOfAdministration_Infusion,
  /**
   * Inhalation
   */
  Inhalation: v3_RouteOfAdministration_Inhalation,
  /**
   * Injection
   */
  Injection: v3_RouteOfAdministration_Injection,
  /**
   * Insertion
   */
  Insertion: v3_RouteOfAdministration_Insertion,
  /**
   * Instillation
   */
  Instillation: v3_RouteOfAdministration_Instillation,
  /**
   * Interameningeal
   */
  InterameningealRoute: v3_RouteOfAdministration_InterameningealRoute,
  /**
   * Interstitial
   */
  InterstitialRoute: v3_RouteOfAdministration_InterstitialRoute,
  /**
   * Intra-abdominal
   */
  IntraabdominalRoute: v3_RouteOfAdministration_IntraabdominalRoute,
  /**
   * Intra-arterial
   */
  IntraarterialRoute: v3_RouteOfAdministration_IntraarterialRoute,
  /**
   * Intraarticular
   */
  IntraarticularRoute: v3_RouteOfAdministration_IntraarticularRoute,
  /**
   * Intrabronchial
   */
  IntrabronchialRoute: v3_RouteOfAdministration_IntrabronchialRoute,
  /**
   * Intrabursal
   */
  IntrabursalRoute: v3_RouteOfAdministration_IntrabursalRoute,
  /**
   * Intracardiac
   */
  IntracardiacRoute: v3_RouteOfAdministration_IntracardiacRoute,
  /**
   * Intracartilaginous
   */
  IntracartilaginousRoute: v3_RouteOfAdministration_IntracartilaginousRoute,
  /**
   * Intracaudal
   */
  IntracaudalRoute: v3_RouteOfAdministration_IntracaudalRoute,
  /**
   * Intracavernosal
   */
  IntracavernosalRoute: v3_RouteOfAdministration_IntracavernosalRoute,
  /**
   * Intracavitary
   */
  IntracavitaryRoute: v3_RouteOfAdministration_IntracavitaryRoute,
  /**
   * Intracerebral
   */
  IntracerebralRoute: v3_RouteOfAdministration_IntracerebralRoute,
  /**
   * Intracervical
   */
  IntracervicalRoute: v3_RouteOfAdministration_IntracervicalRoute,
  /**
   * Intracisternal
   */
  IntracisternalRoute: v3_RouteOfAdministration_IntracisternalRoute,
  /**
   * Intracorneal
   */
  IntracornealRoute: v3_RouteOfAdministration_IntracornealRoute,
  /**
   * Intracoronal (dental)
   */
  IntracoronalRoute: v3_RouteOfAdministration_IntracoronalRoute,
  /**
   * Intracoronary
   */
  IntracoronaryRoute: v3_RouteOfAdministration_IntracoronaryRoute,
  /**
   * Intracorpus cavernosum
   */
  IntracorpusCavernosumRoute: v3_RouteOfAdministration_IntracorpusCavernosumRoute,
  /**
   * Intradermal
   */
  IntradermalRoute: v3_RouteOfAdministration_IntradermalRoute,
  /**
   * Intradiscal
   */
  IntradiscalRoute: v3_RouteOfAdministration_IntradiscalRoute,
  /**
   * Intraductal
   */
  IntraductalRoute: v3_RouteOfAdministration_IntraductalRoute,
  /**
   * Intraduodenal
   */
  IntraduodenalRoute: v3_RouteOfAdministration_IntraduodenalRoute,
  /**
   * Intradural
   */
  IntraduralRoute: v3_RouteOfAdministration_IntraduralRoute,
  /**
   * Intraepidermal
   */
  IntraepidermalRoute: v3_RouteOfAdministration_IntraepidermalRoute,
  /**
   * Intraepithelial
   */
  IntraepithelialRoute: v3_RouteOfAdministration_IntraepithelialRoute,
  /**
   * Intraesophageal
   */
  IntraesophagealRoute: v3_RouteOfAdministration_IntraesophagealRoute,
  /**
   * Intragastric
   */
  IntragastricRoute: v3_RouteOfAdministration_IntragastricRoute,
  /**
   * Intraileal
   */
  IntrailealRoute: v3_RouteOfAdministration_IntrailealRoute,
  /**
   * Intralesional
   */
  IntralesionalRoute: v3_RouteOfAdministration_IntralesionalRoute,
  /**
   * Intraluminal
   */
  IntraluminalRoute: v3_RouteOfAdministration_IntraluminalRoute,
  /**
   * Intralymphatic
   */
  IntralymphaticRoute: v3_RouteOfAdministration_IntralymphaticRoute,
  /**
   * Intramedullary
   */
  IntramedullaryRoute: v3_RouteOfAdministration_IntramedullaryRoute,
  /**
   * Intramuscular
   */
  IntramuscularRoute: v3_RouteOfAdministration_IntramuscularRoute,
  /**
   * Intraocular
   */
  IntraocularRoute: v3_RouteOfAdministration_IntraocularRoute,
  /**
   * Intraosseous
   */
  IntraosseousRoute: v3_RouteOfAdministration_IntraosseousRoute,
  /**
   * Intraovarian
   */
  IntraovarianRoute: v3_RouteOfAdministration_IntraovarianRoute,
  /**
   * Intrapericardial
   */
  IntrapericardialRoute: v3_RouteOfAdministration_IntrapericardialRoute,
  /**
   * Intraperitoneal
   */
  IntraperitonealRoute: v3_RouteOfAdministration_IntraperitonealRoute,
  /**
   * Intrapleural
   */
  IntrapleuralRoute: v3_RouteOfAdministration_IntrapleuralRoute,
  /**
   * Intraprostatic
   */
  IntraprostaticRoute: v3_RouteOfAdministration_IntraprostaticRoute,
  /**
   * Intrapulmonary
   */
  IntrapulmonaryRoute: v3_RouteOfAdministration_IntrapulmonaryRoute,
  /**
   * Intrasinal
   */
  IntrasinalRoute: v3_RouteOfAdministration_IntrasinalRoute,
  /**
   * Intraspinal
   */
  IntraspinalRoute: v3_RouteOfAdministration_IntraspinalRoute,
  /**
   * Intrasternal
   */
  IntrasternalRoute: v3_RouteOfAdministration_IntrasternalRoute,
  /**
   * Intrasynovial
   */
  IntrasynovialRoute: v3_RouteOfAdministration_IntrasynovialRoute,
  /**
   * Intratendinous
   */
  IntratendinousRoute: v3_RouteOfAdministration_IntratendinousRoute,
  /**
   * Intratesticular
   */
  IntratesticularRoute: v3_RouteOfAdministration_IntratesticularRoute,
  /**
   * Intrathecal
   */
  IntrathecalRoute: v3_RouteOfAdministration_IntrathecalRoute,
  /**
   * Intrathoracic
   */
  IntrathoracicRoute: v3_RouteOfAdministration_IntrathoracicRoute,
  /**
   * Intratracheal
   */
  IntratrachealRoute: v3_RouteOfAdministration_IntratrachealRoute,
  /**
   * Intratubular
   */
  IntratubularRoute: v3_RouteOfAdministration_IntratubularRoute,
  /**
   * Intratumor
   */
  IntratumorRoute: v3_RouteOfAdministration_IntratumorRoute,
  /**
   * Intratympanic
   */
  IntratympanicRoute: v3_RouteOfAdministration_IntratympanicRoute,
  /**
   * Intrauterine
   */
  IntrauterineRoute: v3_RouteOfAdministration_IntrauterineRoute,
  /**
   * Intravascular
   */
  IntravascularRoute: v3_RouteOfAdministration_IntravascularRoute,
  /**
   * Intravenous
   */
  IntravenousRoute: v3_RouteOfAdministration_IntravenousRoute,
  /**
   * Intraventricular
   */
  IntraventricularRoute: v3_RouteOfAdministration_IntraventricularRoute,
  /**
   * Intravesicle
   */
  IntravesicleRoute: v3_RouteOfAdministration_IntravesicleRoute,
  /**
   * Intravitreal
   */
  IntravitrealRoute: v3_RouteOfAdministration_IntravitrealRoute,
  /**
   * Iontophoresis
   */
  IontophoresisRoute: v3_RouteOfAdministration_IontophoresisRoute,
  /**
   * Irrigation
   */
  Irrigation: v3_RouteOfAdministration_Irrigation,
  /**
   * Jejunum
   */
  JejunumRoute: v3_RouteOfAdministration_JejunumRoute,
  /**
   * Lacrimal puncta
   */
  LacrimalPunctaRoute: v3_RouteOfAdministration_LacrimalPunctaRoute,
  /**
   * Laryngeal
   */
  LaryngealRoute: v3_RouteOfAdministration_LaryngealRoute,
  /**
   * Lavage
   */
  LavageRoute: v3_RouteOfAdministration_LavageRoute,
  /**
   * Lingual
   */
  LingualRoute: v3_RouteOfAdministration_LingualRoute,
  /**
   * Mucosal absorption
   */
  MucosalAbsorptionRoute: v3_RouteOfAdministration_MucosalAbsorptionRoute,
  /**
   * Mucous membrane
   */
  MucousMembraneRoute: v3_RouteOfAdministration_MucousMembraneRoute,
  /**
   * Nail
   */
  NailRoute: v3_RouteOfAdministration_NailRoute,
  /**
   * Nasal
   */
  NasalRoute: v3_RouteOfAdministration_NasalRoute,
  /**
   * Nebulization
   */
  Nebulization: v3_RouteOfAdministration_Nebulization,
  /**
   * Ophthalmic
   */
  OphthalmicRoute: v3_RouteOfAdministration_OphthalmicRoute,
  /**
   * Oral
   */
  OralRoute: v3_RouteOfAdministration_OralRoute,
  /**
   * Oromucosal
   */
  OromucosalRoute: v3_RouteOfAdministration_OromucosalRoute,
  /**
   * Oropharyngeal
   */
  OropharyngealRoute: v3_RouteOfAdministration_OropharyngealRoute,
  /**
   * Otic
   */
  OticRoute: v3_RouteOfAdministration_OticRoute,
  /**
   * Paranasal sinuses
   */
  ParanasalSinusesRoute: v3_RouteOfAdministration_ParanasalSinusesRoute,
  /**
   * Parenteral
   */
  ParenteralRoute: v3_RouteOfAdministration_ParenteralRoute,
  /**
   * Perianal
   */
  PerianalRoute: v3_RouteOfAdministration_PerianalRoute,
  /**
   * Periarticular
   */
  PeriarticularRoute: v3_RouteOfAdministration_PeriarticularRoute,
  /**
   * Peridural
   */
  PeriduralRoute: v3_RouteOfAdministration_PeriduralRoute,
  /**
   * Perineal
   */
  PerinealRoute: v3_RouteOfAdministration_PerinealRoute,
  /**
   * Perineural
   */
  PerineuralRoute: v3_RouteOfAdministration_PerineuralRoute,
  /**
   * Periodontal
   */
  PeriodontalRoute: v3_RouteOfAdministration_PeriodontalRoute,
  /**
   * Pulmonary
   */
  PulmonaryRoute: v3_RouteOfAdministration_PulmonaryRoute,
  /**
   * Rectal
   */
  RectalRoute: v3_RouteOfAdministration_RectalRoute,
  /**
   * Respiratory tract
   */
  RespiratoryTractRoute: v3_RouteOfAdministration_RespiratoryTractRoute,
  /**
   * Retrobulbar
   */
  RetrobulbarRoute: v3_RouteOfAdministration_RetrobulbarRoute,
  /**
   * Rinse
   */
  Rinse: v3_RouteOfAdministration_Rinse,
  /**
   * Route of substance administration classified by administration method.
   */
  RouteByMethod: v3_RouteOfAdministration_RouteByMethod,
  /**
   * Route of substance administration classified by site.
   */
  RouteBySite: v3_RouteOfAdministration_RouteBySite,
  /**
   * Scalp
   */
  ScalpRoute: v3_RouteOfAdministration_ScalpRoute,
  /**
   * Sinus, unspecified
   */
  SinusUnspecifiedRoute: v3_RouteOfAdministration_SinusUnspecifiedRoute,
  /**
   * Skin
   */
  SkinRoute: v3_RouteOfAdministration_SkinRoute,
  /**
   * Soft tissue
   */
  SoftTissueRoute: v3_RouteOfAdministration_SoftTissueRoute,
  /**
   * Subarachnoid
   */
  SubarachnoidRoute: v3_RouteOfAdministration_SubarachnoidRoute,
  /**
   * Subconjunctival
   */
  SubconjunctivalRoute: v3_RouteOfAdministration_SubconjunctivalRoute,
  /**
   * Subcutaneous
   */
  SubcutaneousRoute: v3_RouteOfAdministration_SubcutaneousRoute,
  /**
   * Sublesional
   */
  SublesionalRoute: v3_RouteOfAdministration_SublesionalRoute,
  /**
   * Sublingual
   */
  SublingualRoute: v3_RouteOfAdministration_SublingualRoute,
  /**
   * Submucosal
   */
  SubmucosalRoute: v3_RouteOfAdministration_SubmucosalRoute,
  /**
   * Suppository
   */
  SuppositoryRoute: v3_RouteOfAdministration_SuppositoryRoute,
  /**
   * Swish
   */
  Swish: v3_RouteOfAdministration_Swish,
  /**
   * Topical absorption
   */
  TopicalAbsorptionRoute: v3_RouteOfAdministration_TopicalAbsorptionRoute,
  /**
   * Topical application
   */
  TopicalApplication: v3_RouteOfAdministration_TopicalApplication,
  /**
   * Tracheostomy
   */
  TracheostomyRoute: v3_RouteOfAdministration_TracheostomyRoute,
  /**
   * Transmucosal
   */
  TransmucosalRoute: v3_RouteOfAdministration_TransmucosalRoute,
  /**
   * Transplacental
   */
  TransplacentalRoute: v3_RouteOfAdministration_TransplacentalRoute,
  /**
   * Transtracheal
   */
  TranstrachealRoute: v3_RouteOfAdministration_TranstrachealRoute,
  /**
   * Transtympanic
   */
  TranstympanicRoute: v3_RouteOfAdministration_TranstympanicRoute,
  /**
   * Ureteral
   */
  UreteralRoute: v3_RouteOfAdministration_UreteralRoute,
  /**
   * Urethral
   */
  UrethralRoute: v3_RouteOfAdministration_UrethralRoute,
  /**
   * Urinary bladder
   */
  UrinaryBladderRoute: v3_RouteOfAdministration_UrinaryBladderRoute,
  /**
   * Urinary tract
   */
  UrinaryTractRoute: v3_RouteOfAdministration_UrinaryTractRoute,
  /**
   * Vaginal
   */
  VaginalRoute: v3_RouteOfAdministration_VaginalRoute,
  /**
   * Vitreous humour
   */
  VitreousHumourRoute: v3_RouteOfAdministration_VitreousHumourRoute,
  /**
   * Injection, amniotic fluid
   */
  InjectionAmnioticFluid: v3_RouteOfAdministration_InjectionAmnioticFluid,
  /**
   * Injection, biliary tract
   */
  InjectionBiliaryTract: v3_RouteOfAdministration_InjectionBiliaryTract,
  /**
   * Injection, urinary bladder
   */
  InjectionUrinaryBladder: v3_RouteOfAdministration_InjectionUrinaryBladder,
  /**
   * Instillation, urinary catheter
   */
  InstillationUrinaryCatheter: v3_RouteOfAdministration_InstillationUrinaryCatheter,
  /**
   * Irrigation, urinary bladder
   */
  IrrigationUrinaryBladder: v3_RouteOfAdministration_IrrigationUrinaryBladder,
  /**
   * Irrigation, urinary bladder, continuous
   */
  IrrigationUrinaryBladderContinuous: v3_RouteOfAdministration_IrrigationUrinaryBladderContinuous,
  /**
   * Irrigation, urinary bladder, tidal
   */
  IrrigationUrinaryBladderTidal: v3_RouteOfAdministration_IrrigationUrinaryBladderTidal,
  /**
   * Topical application, buccal
   */
  TopicalApplicationBuccal: v3_RouteOfAdministration_TopicalApplicationBuccal,
  /**
   * Instillation, continuous ambulatory peritoneal dialysis port
   */
  InstillationContinuousAmbulatoryPeritonealDialysisPort: v3_RouteOfAdministration_InstillationContinuousAmbulatoryPeritonealDialysisPort,
  /**
   * Instillation, cecostomy
   */
  InstillationCecostomy: v3_RouteOfAdministration_InstillationCecostomy,
  /**
   * Topical application, cervical
   */
  TopicalApplicationCervical: v3_RouteOfAdministration_TopicalApplicationCervical,
  /**
   * Injection, cervical
   */
  InjectionCervical: v3_RouteOfAdministration_InjectionCervical,
  /**
   * Insertion, cervical (uterine)
   */
  InsertionCervicalUterine: v3_RouteOfAdministration_InsertionCervicalUterine,
  /**
   * Chew, oral
   */
  ChewOral: v3_RouteOfAdministration_ChewOral,
  /**
   * Injection, for cholangiography
   */
  InjectionForCholangiography: v3_RouteOfAdministration_InjectionForCholangiography,
  /**
   * Instillation, chest tube
   */
  InstillationChestTube: v3_RouteOfAdministration_InstillationChestTube,
  /**
   * Topical application, dental
   */
  TopicalApplicationDental: v3_RouteOfAdministration_TopicalApplicationDental,
  /**
   * Rinse, dental
   */
  RinseDental: v3_RouteOfAdministration_RinseDental,
  /**
   * Dissolve, oral
   */
  DissolveOral: v3_RouteOfAdministration_DissolveOral,
  /**
   * Douche, vaginal
   */
  DoucheVaginal: v3_RouteOfAdministration_DoucheVaginal,
  /**
   * Topical application, soaked dressing
   */
  TopicalApplicationSoakedDressing: v3_RouteOfAdministration_TopicalApplicationSoakedDressing,
  /**
   * Instillation, enteral feeding tube
   */
  InstillationEnteralFeedingTube: v3_RouteOfAdministration_InstillationEnteralFeedingTube,
  /**
   * Electro-osmosis
   */
  ElectroOsmosis: v3_RouteOfAdministration_ElectroOsmosis,
  /**
   * Injection, endosinusial
   */
  InjectionEndosinusial: v3_RouteOfAdministration_InjectionEndosinusial,
  /**
   * Enema, rectal
   */
  EnemaRectal: v3_RouteOfAdministration_EnemaRectal,
  /**
   * Instillation, enteral
   */
  InstillationEnteral: v3_RouteOfAdministration_InstillationEnteral,
  /**
   * Infusion, epidural
   */
  InfusionEpidural: v3_RouteOfAdministration_InfusionEpidural,
  /**
   * Injection, epidural
   */
  InjectionEpidural: v3_RouteOfAdministration_InjectionEpidural,
  /**
   * Injection, epidural, push
   */
  InjectionEpiduralPush: v3_RouteOfAdministration_InjectionEpiduralPush,
  /**
   * Injection, epidural, slow push
   */
  InjectionEpiduralSlowPush: v3_RouteOfAdministration_InjectionEpiduralSlowPush,
  /**
   * Instillation, endotracheal tube
   */
  InstillationEndotrachealTube: v3_RouteOfAdministration_InstillationEndotrachealTube,
  /**
   * Nebulization, endotracheal tube
   */
  NebulizationEndotrachealTube: v3_RouteOfAdministration_NebulizationEndotrachealTube,
  /**
   * Diffusion, extracorporeal
   */
  DiffusionExtracorporeal: v3_RouteOfAdministration_DiffusionExtracorporeal,
  /**
   * Injection, extracorporeal
   */
  InjectionExtracorporeal: v3_RouteOfAdministration_InjectionExtracorporeal,
  /**
   * Injection, extra-amniotic
   */
  InjectionExtraAmniotic: v3_RouteOfAdministration_InjectionExtraAmniotic,
  /**
   * Gargle
   */
  Gargle: v3_RouteOfAdministration_Gargle,
  /**
   * Injection, gastric button
   */
  InjectionGastricButton: v3_RouteOfAdministration_InjectionGastricButton,
  /**
   * Topical application, gingival
   */
  TopicalApplicationGingival: v3_RouteOfAdministration_TopicalApplicationGingival,
  /**
   * Injection, gingival
   */
  InjectionGingival: v3_RouteOfAdministration_InjectionGingival,
  /**
   * Instillation, gastro-jejunostomy tube
   */
  InstillationGastroJejunostomyTube: v3_RouteOfAdministration_InstillationGastroJejunostomyTube,
  /**
   * Instillation, gastrostomy tube
   */
  InstillationGastrostomyTube: v3_RouteOfAdministration_InstillationGastrostomyTube,
  /**
   * Irrigation, genitourinary
   */
  IrrigationGenitourinary: v3_RouteOfAdministration_IrrigationGenitourinary,
  /**
   * Topical application, hair
   */
  TopicalApplicationHair: v3_RouteOfAdministration_TopicalApplicationHair,
  /**
   * Diffusion, hemodialysis
   */
  DiffusionHemodialysis: v3_RouteOfAdministration_DiffusionHemodialysis,
  /**
   * Injection, hemodialysis port
   */
  InjectionHemodialysisPort: v3_RouteOfAdministration_InjectionHemodialysisPort,
  /**
   * Infusion, intraarterial catheter
   */
  InfusionIntraarterialCatheter: v3_RouteOfAdministration_InfusionIntraarterialCatheter,
  /**
   * Injection, intra-abdominal
   */
  InjectionIntraAbdominal: v3_RouteOfAdministration_InjectionIntraAbdominal,
  /**
   * Injection, intraarterial
   */
  InjectionIntraarterial: v3_RouteOfAdministration_InjectionIntraarterial,
  /**
   * Injection, intraarterial, push
   */
  InjectionIntraarterialPush: v3_RouteOfAdministration_InjectionIntraarterialPush,
  /**
   * Injection, intraarterial, slow push
   */
  InjectionIntraarterialSlowPush: v3_RouteOfAdministration_InjectionIntraarterialSlowPush,
  /**
   * Injection, intraarticular
   */
  InjectionIntraarticular: v3_RouteOfAdministration_InjectionIntraarticular,
  /**
   * Instillation, intrabronchial
   */
  InstillationIntrabronchial: v3_RouteOfAdministration_InstillationIntrabronchial,
  /**
   * Injection, intrabursal
   */
  InjectionIntrabursal: v3_RouteOfAdministration_InjectionIntrabursal,
  /**
   * Infusion, intracardiac
   */
  InfusionIntracardiac: v3_RouteOfAdministration_InfusionIntracardiac,
  /**
   * Injection, intracardiac
   */
  InjectionIntracardiac: v3_RouteOfAdministration_InjectionIntracardiac,
  /**
   * Injection, intracardiac, rapid push
   */
  InjectionIntracardiacRapidPush: v3_RouteOfAdministration_InjectionIntracardiacRapidPush,
  /**
   * Injection, intracardiac, slow push
   */
  InjectionIntracardiacSlowPush: v3_RouteOfAdministration_InjectionIntracardiacSlowPush,
  /**
   * Injection, intracardiac, push
   */
  InjectionIntracardiacPush: v3_RouteOfAdministration_InjectionIntracardiacPush,
  /**
   * Injection, intracartilaginous
   */
  InjectionIntracartilaginous: v3_RouteOfAdministration_InjectionIntracartilaginous,
  /**
   * Injection, intracaudal
   */
  InjectionIntracaudal: v3_RouteOfAdministration_InjectionIntracaudal,
  /**
   * Injection, intracavernous
   */
  InjectionIntracavernous: v3_RouteOfAdministration_InjectionIntracavernous,
  /**
   * Injection, intracavitary
   */
  InjectionIntracavitary: v3_RouteOfAdministration_InjectionIntracavitary,
  /**
   * Injection, intracerebral
   */
  InjectionIntracerebral: v3_RouteOfAdministration_InjectionIntracerebral,
  /**
   * Injection, intracisternal
   */
  InjectionIntracisternal: v3_RouteOfAdministration_InjectionIntracisternal,
  /**
   * Infusion, intracoronary
   */
  InfusionIntracoronary: v3_RouteOfAdministration_InfusionIntracoronary,
  /**
   * Topical application, intracorneal
   */
  TopicalApplicationIntracorneal: v3_RouteOfAdministration_TopicalApplicationIntracorneal,
  /**
   * Injection, intracoronary
   */
  InjectionIntracoronary: v3_RouteOfAdministration_InjectionIntracoronary,
  /**
   * Injection, intracoronary, push
   */
  InjectionIntracoronaryPush: v3_RouteOfAdministration_InjectionIntracoronaryPush,
  /**
   * Topical application, intracoronal (dental)
   */
  TopicalApplicationIntracoronalDental: v3_RouteOfAdministration_TopicalApplicationIntracoronalDental,
  /**
   * Injection, intracorpus cavernosum
   */
  InjectionIntracorpusCavernosum: v3_RouteOfAdministration_InjectionIntracorpusCavernosum,
  /**
   * Implantation, intradermal
   */
  ImplantationIntradermal: v3_RouteOfAdministration_ImplantationIntradermal,
  /**
   * Injection, intradermal
   */
  InjectionIntradermal: v3_RouteOfAdministration_InjectionIntradermal,
  /**
   * Injection, intradiscal
   */
  InjectionIntradiscal: v3_RouteOfAdministration_InjectionIntradiscal,
  /**
   * Mucosal absorption, intraduodenal
   */
  MucosalAbsorptionIntraduodenal: v3_RouteOfAdministration_MucosalAbsorptionIntraduodenal,
  /**
   * Injection, intraductal
   */
  InjectionIntraductal: v3_RouteOfAdministration_InjectionIntraductal,
  /**
   * Instillation, intraduodenal
   */
  InstillationIntraduodenal: v3_RouteOfAdministration_InstillationIntraduodenal,
  /**
   * Injection, intradural
   */
  InjectionIntradural: v3_RouteOfAdministration_InjectionIntradural,
  /**
   * Injection, intraepidermal
   */
  InjectionIntraepidermal: v3_RouteOfAdministration_InjectionIntraepidermal,
  /**
   * Injection, intraepithelial
   */
  InjectionIntraepithelial: v3_RouteOfAdministration_InjectionIntraepithelial,
  /**
   * Instillation, intraesophageal
   */
  InstillationIntraesophageal: v3_RouteOfAdministration_InstillationIntraesophageal,
  /**
   * Topical application, intraesophageal
   */
  TopicalApplicationIntraesophageal: v3_RouteOfAdministration_TopicalApplicationIntraesophageal,
  /**
   * Instillation, intragastric
   */
  InstillationIntragastric: v3_RouteOfAdministration_InstillationIntragastric,
  /**
   * Irrigation, intragastric
   */
  IrrigationIntragastric: v3_RouteOfAdministration_IrrigationIntragastric,
  /**
   * Lavage, intragastric
   */
  LavageIntragastric: v3_RouteOfAdministration_LavageIntragastric,
  /**
   * Instillation, intraileal
   */
  InstillationIntraileal: v3_RouteOfAdministration_InstillationIntraileal,
  /**
   * Topical application, intraileal
   */
  TopicalApplicationIntraileal: v3_RouteOfAdministration_TopicalApplicationIntraileal,
  /**
   * Injection, intralesional
   */
  InjectionIntralesional: v3_RouteOfAdministration_InjectionIntralesional,
  /**
   * Irrigation, intralesional
   */
  IrrigationIntralesional: v3_RouteOfAdministration_IrrigationIntralesional,
  /**
   * Topical application, intralesional
   */
  TopicalApplicationIntralesional: v3_RouteOfAdministration_TopicalApplicationIntralesional,
  /**
   * Injection, intraluminal
   */
  InjectionIntraluminal: v3_RouteOfAdministration_InjectionIntraluminal,
  /**
   * Topical application, intraluminal
   */
  TopicalApplicationIntraluminal: v3_RouteOfAdministration_TopicalApplicationIntraluminal,
  /**
   * Injection, intralymphatic
   */
  InjectionIntralymphatic: v3_RouteOfAdministration_InjectionIntralymphatic,
  /**
   * Injection, intramuscular
   */
  InjectionIntramuscular: v3_RouteOfAdministration_InjectionIntramuscular,
  /**
   * Injection, intramuscular, deep
   */
  InjectionIntramuscularDeep: v3_RouteOfAdministration_InjectionIntramuscularDeep,
  /**
   * Injection, intramedullary
   */
  InjectionIntramedullary: v3_RouteOfAdministration_InjectionIntramedullary,
  /**
   * Injection, intramuscular, z track
   */
  InjectionIntramuscularZTrack: v3_RouteOfAdministration_InjectionIntramuscularZTrack,
  /**
   * Insufflation
   */
  Insufflation: v3_RouteOfAdministration_Insufflation,
  /**
   * Injection, interameningeal
   */
  InjectionInterameningeal: v3_RouteOfAdministration_InjectionInterameningeal,
  /**
   * Injection, interstitial
   */
  InjectionInterstitial: v3_RouteOfAdministration_InjectionInterstitial,
  /**
   * Injection, intraocular
   */
  InjectionIntraocular: v3_RouteOfAdministration_InjectionIntraocular,
  /**
   * Instillation, intraocular
   */
  InstillationIntraocular: v3_RouteOfAdministration_InstillationIntraocular,
  /**
   * Irrigation, intraocular
   */
  IrrigationIntraocular: v3_RouteOfAdministration_IrrigationIntraocular,
  /**
   * Topical application, iontophoresis
   */
  TopicalApplicationIontophoresis: v3_RouteOfAdministration_TopicalApplicationIontophoresis,
  /**
   * Infusion, intraosseous, continuous
   */
  InfusionIntraosseousContinuous: v3_RouteOfAdministration_InfusionIntraosseousContinuous,
  /**
   * Injection, intraosseous
   */
  InjectionIntraosseous: v3_RouteOfAdministration_InjectionIntraosseous,
  /**
   * Insertion, intraocular, surgical
   */
  InsertionIntraocularSurgical: v3_RouteOfAdministration_InsertionIntraocularSurgical,
  /**
   * Topical application, intraocular
   */
  TopicalApplicationIntraocular: v3_RouteOfAdministration_TopicalApplicationIntraocular,
  /**
   * Injection, intraovarian
   */
  InjectionIntraovarian: v3_RouteOfAdministration_InjectionIntraovarian,
  /**
   * Injection, intrapericardial
   */
  InjectionIntrapericardial: v3_RouteOfAdministration_InjectionIntrapericardial,
  /**
   * Injection, intraperitoneal
   */
  InjectionIntraperitoneal: v3_RouteOfAdministration_InjectionIntraperitoneal,
  /**
   * Inhalation, oral
   */
  InhalationRespiratory: v3_RouteOfAdministration_InhalationRespiratory,
  /**
   * Injection, intrapulmonary
   */
  InjectionIntrapulmonary: v3_RouteOfAdministration_InjectionIntrapulmonary,
  /**
   * Injection, intrapleural
   */
  InjectionIntrapleural: v3_RouteOfAdministration_InjectionIntrapleural,
  /**
   * Inhalation, intermittent positive pressure breathing (ippb)
   */
  InhalationIntermittentPositivePressureBreathingIppb: v3_RouteOfAdministration_InhalationIntermittentPositivePressureBreathingIppb,
  /**
   * Injection, intraprostatic
   */
  InjectionIntraprostatic: v3_RouteOfAdministration_InjectionIntraprostatic,
  /**
   * Injection, insulin pump
   */
  InjectionInsulinPump: v3_RouteOfAdministration_InjectionInsulinPump,
  /**
   * Instillation, intrasinal
   */
  InstillationIntrasinal: v3_RouteOfAdministration_InstillationIntrasinal,
  /**
   * Injection, intraspinal
   */
  InjectionIntraspinal: v3_RouteOfAdministration_InjectionIntraspinal,
  /**
   * Injection, intrasternal
   */
  InjectionIntrasternal: v3_RouteOfAdministration_InjectionIntrasternal,
  /**
   * Injection, intrasynovial
   */
  InjectionIntrasynovial: v3_RouteOfAdministration_InjectionIntrasynovial,
  /**
   * Infusion, intrathecal
   */
  InfusionIntrathecal: v3_RouteOfAdministration_InfusionIntrathecal,
  /**
   * Injection, intratendinous
   */
  InjectionIntratendinous: v3_RouteOfAdministration_InjectionIntratendinous,
  /**
   * Injection, intratesticular
   */
  InjectionIntratesticular: v3_RouteOfAdministration_InjectionIntratesticular,
  /**
   * Injection, intrathoracic
   */
  InjectionIntrathoracic: v3_RouteOfAdministration_InjectionIntrathoracic,
  /**
   * Injection, intrathecal
   */
  InjectionIntrathecal: v3_RouteOfAdministration_InjectionIntrathecal,
  /**
   * Instillation, intratracheal
   */
  InstillationIntratracheal: v3_RouteOfAdministration_InstillationIntratracheal,
  /**
   * Mucosal absorption, intratracheal
   */
  MucosalAbsorptionIntratracheal: v3_RouteOfAdministration_MucosalAbsorptionIntratracheal,
  /**
   * Injection, intratubular
   */
  InjectionIntratubular: v3_RouteOfAdministration_InjectionIntratubular,
  /**
   * Injection, intratumor
   */
  InjectionIntratumor: v3_RouteOfAdministration_InjectionIntratumor,
  /**
   * Injection, intratympanic
   */
  InjectionIntratympanic: v3_RouteOfAdministration_InjectionIntratympanic,
  /**
   * Insertion, intrauterine
   */
  InsertionIntrauterine: v3_RouteOfAdministration_InsertionIntrauterine,
  /**
   * Injection, intracervical (uterus)
   */
  InjectionIntrauterine: v3_RouteOfAdministration_InjectionIntrauterine,
  /**
   * Injection, intracervical (uterus)
   */
  InjectionIntracervicalUterus: v3_RouteOfAdministration_InjectionIntracervicalUterus,
  /**
   * Instillation, intrauterine
   */
  InstillationIntrauterine: v3_RouteOfAdministration_InstillationIntrauterine,
  /**
   * Injection, intraureteral, retrograde
   */
  InjectionIntraureteralRetrograde: v3_RouteOfAdministration_InjectionIntraureteralRetrograde,
  /**
   * Infusion, intravenous
   */
  InfusionIntravenous: v3_RouteOfAdministration_InfusionIntravenous,
  /**
   * Infusion, intravascular
   */
  InfusionIntravascular: v3_RouteOfAdministration_InfusionIntravascular,
  /**
   * Injection, intravascular
   */
  InjectionIntravascular: v3_RouteOfAdministration_InjectionIntravascular,
  /**
   * Infusion, intravenous catheter
   */
  InfusionIntravenousCatheter: v3_RouteOfAdministration_InfusionIntravenousCatheter,
  /**
   * Infusion, intravenous catheter, continuous
   */
  InfusionIntravenousCatheterContinuous: v3_RouteOfAdministration_InfusionIntravenousCatheterContinuous,
  /**
   * Infusion, intravenous catheter, intermittent
   */
  InfusionIntravenousCatheterIntermittent: v3_RouteOfAdministration_InfusionIntravenousCatheterIntermittent,
  /**
   * Injection, intraventricular (heart)
   */
  InjectionIntraventricularHeart: v3_RouteOfAdministration_InjectionIntraventricularHeart,
  /**
   * Injection, intravesicle
   */
  InjectionIntravesicle: v3_RouteOfAdministration_InjectionIntravesicle,
  /**
   * Flush, intravenous catheter
   */
  FlushIntravenousCatheter: v3_RouteOfAdministration_FlushIntravenousCatheter,
  /**
   * Injection, intravenous
   */
  InjectionIntravenous: v3_RouteOfAdministration_InjectionIntravenous,
  /**
   * Injection, intravenous, bolus
   */
  InjectionIntravenousBolus: v3_RouteOfAdministration_InjectionIntravenousBolus,
  /**
   * Implantation, intravitreal
   */
  ImplantationIntravitreal: v3_RouteOfAdministration_ImplantationIntravitreal,
  /**
   * Injection, intravitreal
   */
  InjectionIntravitreal: v3_RouteOfAdministration_InjectionIntravitreal,
  /**
   * Injection, intravenous, push
   */
  InjectionIntravenousPush: v3_RouteOfAdministration_InjectionIntravenousPush,
  /**
   * Injection, intravenous, rapid push
   */
  InjectionIntravenousRapidPush: v3_RouteOfAdministration_InjectionIntravenousRapidPush,
  /**
   * Injection, intravenous, slow push
   */
  InjectionIntravenousSlowPush: v3_RouteOfAdministration_InjectionIntravenousSlowPush,
  /**
   * Instillation, jejunostomy tube
   */
  InstillationJejunostomyTube: v3_RouteOfAdministration_InstillationJejunostomyTube,
  /**
   * Instillation, laryngeal
   */
  InstillationLaryngeal: v3_RouteOfAdministration_InstillationLaryngeal,
  /**
   * Topical application, laryngeal
   */
  TopicalApplicationLaryngeal: v3_RouteOfAdministration_TopicalApplicationLaryngeal,
  /**
   * Insertion, lacrimal puncta
   */
  InsertionLacrimalPuncta: v3_RouteOfAdministration_InsertionLacrimalPuncta,
  /**
   * Topical application, mucous membrane
   */
  TopicalApplicationMucousMembrane: v3_RouteOfAdministration_TopicalApplicationMucousMembrane,
  /**
   * Topical application, nail
   */
  TopicalApplicationNail: v3_RouteOfAdministration_TopicalApplicationNail,
  /**
   * Topical application, nasal
   */
  TopicalApplicationNasal: v3_RouteOfAdministration_TopicalApplicationNasal,
  /**
   * Instillation, nasal
   */
  InstillationNasal: v3_RouteOfAdministration_InstillationNasal,
  /**
   * Inhalation, nasal
   */
  InhalationNasal: v3_RouteOfAdministration_InhalationNasal,
  /**
   * Inhalation, nasal, prongs
   */
  InhalationNasalCannula: v3_RouteOfAdministration_InhalationNasalCannula,
  /**
   * Inhalation, nebulization, nasal
   */
  InhalationNebulizationNasal: v3_RouteOfAdministration_InhalationNebulizationNasal,
  /**
   * Instillation, nasogastric
   */
  InstillationNasogastric: v3_RouteOfAdministration_InstillationNasogastric,
  /**
   * Inhalation, nebulization
   */
  InhalationNebulization: v3_RouteOfAdministration_InhalationNebulization,
  /**
   * Instillation, nasogastric tube
   */
  InstillationNasogastricTube: v3_RouteOfAdministration_InstillationNasogastricTube,
  /**
   * Instillation, nasotracheal tube
   */
  InstillationNasotrachealTube: v3_RouteOfAdministration_InstillationNasotrachealTube,
  /**
   * Occlusive dressing technique
   */
  OcclusiveDressingTechnique: v3_RouteOfAdministration_OcclusiveDressingTechnique,
  /**
   * Instillation, orogastric tube
   */
  InstillationOrogastricTube: v3_RouteOfAdministration_InstillationOrogastricTube,
  /**
   * Instillation, orojejunum tube
   */
  InstillationOrojejunumTube: v3_RouteOfAdministration_InstillationOrojejunumTube,
  /**
   * Topical application, ophthalmic
   */
  TopicalApplicationOphthalmic: v3_RouteOfAdministration_TopicalApplicationOphthalmic,
  /**
   * Topical application, oral
   */
  TopicalApplicationOral: v3_RouteOfAdministration_TopicalApplicationOral,
  /**
   * Inhalation, oral intermittent flow
   */
  InhalationOralIntermittentFlow: v3_RouteOfAdministration_InhalationOralIntermittentFlow,
  /**
   * Topical application, oromucosal
   */
  TopicalApplicationOromucosal: v3_RouteOfAdministration_TopicalApplicationOromucosal,
  /**
   * Inhalation, nebulization, oral
   */
  InhalationNebulizationOral: v3_RouteOfAdministration_InhalationNebulizationOral,
  /**
   * Topical application, oropharyngeal
   */
  TopicalApplicationOropharyngeal: v3_RouteOfAdministration_TopicalApplicationOropharyngeal,
  /**
   * Rinse, oral
   */
  RinseOral: v3_RouteOfAdministration_RinseOral,
  /**
   * Instillation, otic
   */
  InstillationOtic: v3_RouteOfAdministration_InstillationOtic,
  /**
   * Injection, periarticular
   */
  InjectionPeriarticular: v3_RouteOfAdministration_InjectionPeriarticular,
  /**
   * Injection, parenteral
   */
  InjectionParenteral: v3_RouteOfAdministration_InjectionParenteral,
  /**
   * Infusion, intravenous catheter, pca pump
   */
  InfusionIntravenousCatheterPcaPump: v3_RouteOfAdministration_InfusionIntravenousCatheterPcaPump,
  /**
   * Injection, periodontal
   */
  InjectionPeriodontal: v3_RouteOfAdministration_InjectionPeriodontal,
  /**
   * Topical application, periodontal
   */
  TopicalApplicationPeriodontal: v3_RouteOfAdministration_TopicalApplicationPeriodontal,
  /**
   * Injection, peritoneal dialysis port
   */
  InjectionPeritonealDialysisPort: v3_RouteOfAdministration_InjectionPeritonealDialysisPort,
  /**
   * Instillation, peritoneal dialysis port
   */
  InstillationPeritonealDialysisPort: v3_RouteOfAdministration_InstillationPeritonealDialysisPort,
  /**
   * Injection, peridural
   */
  InjectionPeridural: v3_RouteOfAdministration_InjectionPeridural,
  /**
   * Topical application, perianal
   */
  TopicalApplicationPerianal: v3_RouteOfAdministration_TopicalApplicationPerianal,
  /**
   * Topical application, perineal
   */
  TopicalApplicationPerineal: v3_RouteOfAdministration_TopicalApplicationPerineal,
  /**
   * Injection, perineural
   */
  InjectionPerineural: v3_RouteOfAdministration_InjectionPerineural,
  /**
   * Injection, paranasal sinuses
   */
  InjectionParanasalSinuses: v3_RouteOfAdministration_InjectionParanasalSinuses,
  /**
   * Instillation, paranasal sinuses
   */
  InstillationParanasalSinuses: v3_RouteOfAdministration_InstillationParanasalSinuses,
  /**
   * Swallow, oral
   */
  SwallowOral: v3_RouteOfAdministration_SwallowOral,
  /**
   * Insertion, rectal
   */
  InsertionRectal: v3_RouteOfAdministration_InsertionRectal,
  /**
   * Injection, retrobulbar
   */
  InjectionRetrobulbar: v3_RouteOfAdministration_InjectionRetrobulbar,
  /**
   * Inhalation, oral rebreather mask
   */
  InhalationOralRebreatherMask: v3_RouteOfAdministration_InhalationOralRebreatherMask,
  /**
   * Instillation, rectal
   */
  InstillationRectal: v3_RouteOfAdministration_InstillationRectal,
  /**
   * Irrigation, rectal
   */
  IrrigationRectal: v3_RouteOfAdministration_IrrigationRectal,
  /**
   * Topical application, rectal
   */
  TopicalApplicationRectal: v3_RouteOfAdministration_TopicalApplicationRectal,
  /**
   * Instillation, rectal tube
   */
  InstillationRectalTube: v3_RouteOfAdministration_InstillationRectalTube,
  /**
   * Enema, rectal retention
   */
  EnemaRectalRetention: v3_RouteOfAdministration_EnemaRectalRetention,
  /**
   * Topical application, scalp
   */
  TopicalApplicationScalp: v3_RouteOfAdministration_TopicalApplicationScalp,
  /**
   * Injection, subconjunctival
   */
  InjectionSubconjunctival: v3_RouteOfAdministration_InjectionSubconjunctival,
  /**
   * Shampoo
   */
  Shampoo: v3_RouteOfAdministration_Shampoo,
  /**
   * Instillation, sinus, unspecified
   */
  InstillationSinusUnspecified: v3_RouteOfAdministration_InstillationSinusUnspecified,
  /**
   * Topical application, skin
   */
  TopicalApplicationSkin: v3_RouteOfAdministration_TopicalApplicationSkin,
  /**
   * Dissolve, sublingual
   */
  DissolveSublingual: v3_RouteOfAdministration_DissolveSublingual,
  /**
   * Injection, sublesional
   */
  InjectionSublesional: v3_RouteOfAdministration_InjectionSublesional,
  /**
   * Mucosal absorption, submucosal
   */
  MucosalAbsorptionSubmucosal: v3_RouteOfAdministration_MucosalAbsorptionSubmucosal,
  /**
   * Immersion (soak)
   */
  ImmersionSoak: v3_RouteOfAdministration_ImmersionSoak,
  /**
   * Injection, soft tissue
   */
  InjectionSoftTissue: v3_RouteOfAdministration_InjectionSoftTissue,
  /**
   * Instillation, soft tissue
   */
  InstillationSoftTissue: v3_RouteOfAdministration_InstillationSoftTissue,
  /**
   * Injection, subcutaneous
   */
  InjectionSubcutaneous: v3_RouteOfAdministration_InjectionSubcutaneous,
  /**
   * Implantation, subcutaneous
   */
  ImplantationSubcutaneous: v3_RouteOfAdministration_ImplantationSubcutaneous,
  /**
   * Infusion, subcutaneous
   */
  InfusionSubcutaneous: v3_RouteOfAdministration_InfusionSubcutaneous,
  /**
   * Insertion, subcutaneous, surgical
   */
  InsertionSubcutaneousSurgical: v3_RouteOfAdministration_InsertionSubcutaneousSurgical,
  /**
   * Injection, subarachnoid
   */
  InjectionSubarachnoid: v3_RouteOfAdministration_InjectionSubarachnoid,
  /**
   * Subconjunctival
   */
  Subconjunctival: v3_RouteOfAdministration_Subconjunctival,
  /**
   * Injection, submucosal
   */
  InjectionSubmucosal: v3_RouteOfAdministration_InjectionSubmucosal,
  /**
   * Suck, oromucosal
   */
  SuckOromucosal: v3_RouteOfAdministration_SuckOromucosal,
  /**
   * Topical application, swab
   */
  TopicalApplicationSwab: v3_RouteOfAdministration_TopicalApplicationSwab,
  /**
   * Swish and spit out, oromucosal
   */
  SwishAndSpitOutOromucosal: v3_RouteOfAdministration_SwishAndSpitOutOromucosal,
  /**
   * Swish and swallow, oromucosal
   */
  SwishAndSwallowOromucosal: v3_RouteOfAdministration_SwishAndSwallowOromucosal,
  /**
   * Topical application, transmucosal
   */
  TopicalApplicationTransmucosal: v3_RouteOfAdministration_TopicalApplicationTransmucosal,
  /**
   * Topical
   */
  Topical: v3_RouteOfAdministration_Topical,
  /**
   * Inhalation, tracheostomy
   */
  InhalationTracheostomy: v3_RouteOfAdministration_InhalationTracheostomy,
  /**
   * Instillation, tracheostomy
   */
  InstillationTracheostomy: v3_RouteOfAdministration_InstillationTracheostomy,
  /**
   * Transdermal
   */
  Transdermal: v3_RouteOfAdministration_Transdermal,
  /**
   * Diffusion, transdermal
   */
  DiffusionTransdermal: v3_RouteOfAdministration_DiffusionTransdermal,
  /**
   * Translingual
   */
  Translingual: v3_RouteOfAdministration_Translingual,
  /**
   * Injection, transplacental
   */
  InjectionTransplacental: v3_RouteOfAdministration_InjectionTransplacental,
  /**
   * Injection, transtracheal
   */
  InjectionTranstracheal: v3_RouteOfAdministration_InjectionTranstracheal,
  /**
   * Instillation, transtympanic
   */
  InstillationTranstympanic: v3_RouteOfAdministration_InstillationTranstympanic,
  /**
   * Topical absorption, transtympanic
   */
  TopicalAbsorptionTranstympanic: v3_RouteOfAdministration_TopicalAbsorptionTranstympanic,
  /**
   * Injection, urethral
   */
  InjectionUrethral: v3_RouteOfAdministration_InjectionUrethral,
  /**
   * Insertion, urethral
   */
  InsertionUrethral: v3_RouteOfAdministration_InsertionUrethral,
  /**
   * Instillation, urethral
   */
  InstillationUrethral: v3_RouteOfAdministration_InstillationUrethral,
  /**
   * Suppository, urethral
   */
  SuppositoryUrethral: v3_RouteOfAdministration_SuppositoryUrethral,
  /**
   * Injection, ureteral
   */
  InjectionUreteral: v3_RouteOfAdministration_InjectionUreteral,
  /**
   * Insertion, vaginal
   */
  TopicalApplicationVaginal: v3_RouteOfAdministration_TopicalApplicationVaginal,
  /**
   * Insertion, vaginal
   */
  InsertionVaginal: v3_RouteOfAdministration_InsertionVaginal,
  /**
   * Inhalation, ventilator
   */
  InhalationVentilator: v3_RouteOfAdministration_InhalationVentilator,
  /**
   * Inhalation, ventimask
   */
  InhalationVentimask: v3_RouteOfAdministration_InhalationVentimask,
};
/**
 * No Description Provided
 */
export const V3SecurityIntegrityObservationValue = {
  /**
   * Abstract security metadata observation values used to indicate mechanism used for authorized alteration of an IT resource (data, information object, service, or system capability)
   */
  AlterationIntegrity: v3_ObservationValue_AlterationIntegrity,
  /**
   * Abstract security observation values used to indicate data integrity metadata.
   *
   *
   *                            Examples: Codes conveying the mechanism used to preserve the accuracy and consistency of an IT resource such as a digital signature and a cryptographic hash function.
   */
  DataIntegrity: v3_ObservationValue_DataIntegrity,
  /**
   * Abstract security observation value used to indicate integrity confidence metadata.
   *
   *
   *                            Examples: Codes conveying the level of reliability and trustworthiness of an IT resource.
   */
  IntegrityConfidence: v3_ObservationValue_IntegrityConfidence,
  /**
   * Abstract security provenance metadata observation value used to indicate the entity that asserted an IT resource (data, information object, service, or system capability).
   *
   *
   *                            Examples: Codes conveying the provenance metadata about the entity asserting the resource.
   */
  ProvenanceAssertedBy: v3_ObservationValue_ProvenanceAssertedBy,
  /**
   * Abstract security metadata observation value used to indicate the provenance of an IT resource (data, information object, service, or system capability).
   *
   *
   *                            Examples: Codes conveying the provenance metadata about the entity reporting an IT resource.
   */
  Provenance: v3_ObservationValue_Provenance,
  /**
   * Abstract security provenance metadata observation value used to indicate the entity that reported the resource (data, information object, service, or system capability).
   *
   *
   *                            Examples: Codes conveying the provenance metadata about the entity reporting an IT resource.
   */
  ProvenanceReportedBy: v3_ObservationValue_ProvenanceReportedBy,
  /**
   * Security metadata observation values used to indicate the use of a more abstract version of the content, e.g., replacing exact value of an age or date field with a range, or remove the left digits of a credit card number or SSN.
   */
  Abstracted: v3_ObservationValue_Abstracted,
  /**
   * Security metadata observation values used to indicate the use of an algorithmic combination of actual values with the result of an aggregate function, e.g., average, sum, or count in order to limit disclosure of an IT resource (data, information object, service, or system capability) to the minimum necessary.
   */
  Aggregated: v3_ObservationValue_Aggregated,
  /**
   * Security metadata observation value conveying the alteration integrity of an IT resource (data, information object, service, or system capability) by used to indicate the mechanism by which software systems can strip portions of the resource that could allow the identification of the source of the information or the information subject.  No key to relink the data is retained.
   */
  Anonymized: v3_ObservationValue_Anonymized,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a clinician.
   */
  ClinicianAsserted: v3_ObservationValue_ClinicianAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a clinician.
   */
  ClinicianReported: v3_ObservationValue_ClinicianReported,
  /**
   * Security metadata observation value used to indicate the mechanism by which software systems can establish that data was not modified in transit.
   *
   *
   *                            Rationale: This definition is intended to align with the ISO 22600-2 3.3.19 definition of cryptographic checkvalue: Information which is derived by performing a cryptographic transformation (see cryptography) on the data unit.  The derivation of the checkvalue may be performed in one or more steps and is a result of a mathematical function of the key and a data unit. It is usually used to check the integrity of a data unit.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            SHA-1
   *                            SHA-2 (Secure Hash Algorithm)
   */
  CryptographicHashFunction: v3_ObservationValue_CryptographicHashFunction,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a device.
   */
  DeviceAsserted: v3_ObservationValue_DeviceAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a device.
   */
  DeviceReported: v3_ObservationValue_DeviceReported,
  /**
   * Security metadata observation value used to indicate the mechanism by which software systems use digital signature to establish that data has not been modified.
   *
   *
   *                            Rationale: This definition is intended to align with the ISO 22600-2 3.3.26 definition of digital signature:  Data appended to, or a cryptographic transformation (see cryptography) of, a data unit that allows a recipient of the data unit to prove the source and integrity of the data unit and protect against forgery e.g., by the recipient.
   */
  DigitalSignature: v3_ObservationValue_DigitalSignature,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a healthcare professional.
   */
  HealthcareProfessionalAsserted: v3_ObservationValue_HealthcareProfessionalAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a healthcare professional.
   */
  HealthcareProfessionalReported: v3_ObservationValue_HealthcareProfessionalReported,
  /**
   * Security metadata observation value used to indicate that the veracity or trustworthiness of an IT resource (data, information object, service, or system capability) for a specified purpose of use is perceived to be or deemed by policy to be very high.
   */
  HighlyReliable: v3_ObservationValue_HighlyReliable,
  /**
   * Security metadata observation value used to indicate that the IT resource semantic content has been transformed from one encoding to another.
   *
   *
   *                            Usage Note: "MAP" code does not indicate the semantic fidelity of the transformed content.
   *
   *                         To indicate semantic fidelity for maps of HL7 to other code systems, this security alteration integrity observation may be further specified using an Act valued with Value Set: MapRelationship (2.16.840.1.113883.1.11.11052).
   *
   *                         Semantic fidelity of the mapped IT Resource may also be indicated using a SecurityIntegrityConfidenceObservation.
   */
  Mapped: v3_ObservationValue_Mapped,
  /**
   * Security metadata observation value conveying the alteration integrity of an IT resource (data, information object, service, or system capability) by indicating the mechanism by which software systems can make data unintelligible (that is, as unreadable and unusable by algorithmically transforming plaintext into ciphertext) such that it can only be accessed or used by authorized users.  An authorized user may be provided a key to decrypt per license or "shared secret".
   *
   *
   *                            Usage Note: "MASKED" may be used, per applicable policy, as a flag to indicate to a user or receiver that some portion of an IT resource has been further encrypted, and may be accessed only by an authorized user or receiver to which a decryption key is provided.
   */
  Masked: v3_ObservationValue_Masked,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a patient acquaintance.
   */
  PatientAcquaintanceAsserted: v3_ObservationValue_PatientAcquaintanceAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a patient acquaintance.
   */
  PatientAcquaintanceReported: v3_ObservationValue_PatientAcquaintanceReported,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a patient.
   */
  PatientAsserted: v3_ObservationValue_PatientAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a patient.
   */
  PatientReported: v3_ObservationValue_PatientReported,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a payer.
   */
  PayerAsserted: v3_ObservationValue_PayerAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a payer.
   */
  PayerReported: v3_ObservationValue_PayerReported,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a professional.
   */
  ProfessionalAsserted: v3_ObservationValue_ProfessionalAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a professional.
   */
  ProfessionalReported: v3_ObservationValue_ProfessionalReported,
  /**
   * Security metadata observation value conveying the alteration integrity of an IT resource (data, information object, service, or system capability), by indicating the mechanism by which software systems can strip portions of the resource that could allow the identification of the source of the information or the information subject.  Custodian may retain a key to relink data necessary to reidentify the information subject.
   *
   *
   *                            Rationale: Personal data which has been processed to make it impossible to know whose data it is. Used particularly for secondary use of health data. In some cases, it may be possible for authorized individuals to restore the identity of the individual, e.g.,for public health case management.  Based on ISO/TS 25237:2008 Health informaticsâ€”Pseudonymization
   */
  Pseudonymized: v3_ObservationValue_Pseudonymized,
  /**
   * Security metadata observation value used to indicate the mechanism by which software systems can filter an IT resource (data, information object, service, or system capability) to remove any portion of the resource that is not authorized to be access, used, or disclosed.
   *
   *
   *                            Usage Note: "REDACTED" may be used, per applicable policy, as a flag to indicate to a user or receiver that some portion of an IT resource has filtered and not included in the content accessed or received.
   */
  Redacted: v3_ObservationValue_Redacted,
  /**
   * Security metadata observation value used to indicate that the veracity or trustworthiness of an IT resource (data, information object, service, or system capability) for a specified purpose of use is perceived to be or deemed by policy to be adequate.
   */
  Reliable: v3_ObservationValue_Reliable,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was asserted by a substitute decision maker.
   */
  SubstituteDecisionMakerAsserted: v3_ObservationValue_SubstituteDecisionMakerAsserted,
  /**
   * Security provenance metadata observation value used to indicate that an IT resource (data, information object, service, or system capability) was reported by a substitute decision maker.
   */
  SubstituteDecisionMakerReported: v3_ObservationValue_SubstituteDecisionMakerReported,
  /**
   * Metadata observation used to indicate that some information has been removed from the source object when the view this object contains was constructed because of configuration options when the view was created. The content may not be suitable for use as the basis of a record update
   *
   *
   *                            Usage Note: This is not suitable to be used when information is removed for security reasons - see the code REDACTED for this use.
   */
  Subsetted: v3_ObservationValue_Subsetted,
  /**
   * Security metadata observation value used to indicate that the IT resource syntax has been transformed from one syntactical representation to another.
   *
   *
   *                            Usage Note: "SYNTAC" code does not indicate the syntactical correctness of the syntactically transformed IT resource.
   */
  SyntacticTransform: v3_ObservationValue_SyntacticTransform,
  /**
   * Security metadata observation value used to indicate that the IT resource has been translated from one human language to another.
   *
   *
   *                            Usage Note: "TRSLT" does not indicate the fidelity of the translation or the languages translated.
   *
   *                         The fidelity of the IT Resource translation may be indicated using a SecurityIntegrityConfidenceObservation.
   *
   *                         To indicate languages, use the Value Set:HumanLanguage (2.16.840.1.113883.1.11.11526)
   */
  Translated: v3_ObservationValue_Translated,
  /**
   * Security metadata observation value used to indicate that the veracity or trustworthiness of an IT resource (data, information object, service, or system capability) for a specified purpose of use is perceived to be or deemed by policy to be uncertain.
   */
  UncertainReliability: v3_ObservationValue_UncertainReliability,
  /**
   * Security metadata observation value used to indicate that the veracity or trustworthiness of an IT resource (data, information object, service, or system capability) for a specified purpose of use is perceived to be or deemed by policy to be inadequate.
   */
  Unreliable: v3_ObservationValue_Unreliable,
  /**
   * Security metadata observation value conveying the alteration integrity of an IT resource (data, information object, service, or system capability)  which indicates that the resource only retains versions of an IT resource  for access and use per applicable policy
   *
   *
   *                            Usage Note: When this code is used, expectation is that the system has removed historical versions of the data that falls outside the time period deemed to be the effective time of the applicable version.
   */
  Versioned: v3_ObservationValue_Versioned,
};
/**
 *  Types of security policies that further specify the ActClassPolicy value set.  Examples:
 *   encrypt prohibit redisclosure without consent directive
 */
export const V3SecurityPolicy = {
  /**
   * An access control policy specific to the type of access control scheme, which is used to enforce one or more authorization policies.
   *
   *
   *                            Usage Note: Access control schemes are the type of access control policy, which is comprised of access control policy rules concerning the provision of the access control service.
   *
   *                         There are two categories of access control policies, rule-based and identity-based, which are identified in CCITT Rec. X.800 aka ISO 7498-2. Rule-based access control policies are intended to apply to all access requests by any initiator on any target in a security domain. Identity-based access control policies are based on rules specific to an individual initiator, a group of initiators, entities acting on behalf of initiators, or originators acting in a specific role. Context can modify rule-based or identity-based access control policies. Context rules may define the entire policy in effect. Real systems will usually employ a combination of these policy types; if a rule-based policy is used, then an identity-based policy is usually in effect also.
   *
   *                         An access control scheme may be based on access control lists, capabilities, labels, and context or a combination of these.  An access control scheme is a component of an access control mechanism or "service") along with the supporting mechanisms required by that scheme to provide access control decision information (ADI) supplied by the scheme to the access decision facility (ADF also known as a PDP). (Based on ISO/IEC 10181-3:1996)
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Attribute Based Access Control (ABAC)
   *                            Discretionary Access Control (DAC)
   *                            History Based Access Control (HBAC)
   *                            Identity Based Access Control (IBAC)
   *                            Mandatory Access Control (MAC)
   *                            Organization Based Access Control (OrBAC)
   *                            Relationship Based Access Control (RelBac)
   *                            Responsibility Based Access Control (RespBAC)
   *                            Risk Adaptable Access Control (RAdAC)
   *                         >
   */
  AccessControlScheme: v3_ActCode_AccessControlScheme,
  /**
   * Custodian system must remove any information that could result in identifying the information subject.
   */
  Anonymize: v3_ActCode_Anonymize,
  /**
   * Custodian system must make available to an information subject upon request an accounting of certain disclosures of the individualâ€™s protected health information over a period of time.  Policy may dictate that the accounting include information about the information disclosed,  the date of disclosure, the identification of the receiver, the purpose of the disclosure, the time in which the disclosing entity must provide a response and the time period for which accountings of disclosure can be requested.
   */
  AccountingOfDisclosure: v3_ActCode_AccountingOfDisclosure,
  /**
   * Custodian system must monitor systems to ensure that all users are authorized to operate on information objects.
   */
  Audit: v3_ActCode_Audit,
  /**
   * Custodian system must monitor and maintain retrievable log for each user and operation on information.
   */
  AuditTrail: v3_ActCode_AuditTrail,
  /**
   * Authorisation policies are essentially security policies related to access-control and specify what activities a subject is permitted or forbidden to do, to a set of target objects. They are designed to protect target objects so are interpreted by access control agents or the run-time systems at the target system.
   *
   *                         A positive authorisation policy defines the actions that a subject is permitted to perform on a target. A negative authorisation policy specifies the actions that a subject is forbidden to perform on a target. Positive authorisation policies may also include filters to transform the parameters associated with their actions.  (Based on PONDERS)
   */
  AuthorizationPolicy: v3_ActCode_AuthorizationPolicy,
  /**
   * Custodian security system must retrieve, evaluate, and comply with the information handling directions of the Confidentiality Code associated with an information target.
   */
  ComplyWithConfidentialityCode: v3_ActCode_ComplyWithConfidentialityCode,
  /**
   * Custodian security system must retrieve, evaluate, and comply with applicable information subject consent directives.
   */
  ComplyWithConsentDirective: v3_ActCode_ComplyWithConsentDirective,
  /**
   * Custodian security system must retrieve, evaluate, and comply with applicable jurisdictional privacy policies associated with the target information.
   */
  ComplyWithJurisdictionalPrivacyPolicy: v3_ActCode_ComplyWithJurisdictionalPrivacyPolicy,
  /**
   * Custodian security system must retrieve, evaluate, and comply with applicable organizational privacy policies associated with the target information.
   */
  ComplyWithOrganizationalPrivacyPolicy: v3_ActCode_ComplyWithOrganizationalPrivacyPolicy,
  /**
   * Custodian security system must retrieve, evaluate, and comply with the organizational security policies associated with the target information.
   */
  ComplyWithOrganizationalSecurityPolicy: v3_ActCode_ComplyWithOrganizationalSecurityPolicy,
  /**
   * Custodian security system must retrieve, evaluate, and comply with applicable policies associated with the target information.
   */
  ComplyWithPolicy: v3_ActCode_ComplyWithPolicy,
  /**
   * Custodian security system must declassify information assigned security labels by instantiating a new version of the classified information so as to break the binding of the classifying security label when assigning a new security label that marks the information as unclassified in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the previous assignment and binding.
   */
  DeclassifySecurityLabel: v3_ActCode_DeclassifySecurityLabel,
  /**
   * Custodian system must strip information of data that would allow the identification of the source of the information or the information subject.
   */
  Deidentify: v3_ActCode_Deidentify,
  /**
   * Custodian system must remove target information from access after use.
   */
  DeleteAfterUse: v3_ActCode_DeleteAfterUse,
  /**
   * Delegation policies specify which actions subjects are allowed to delegate to others. A delegation policy thus specifies an authorisation to delegate. Subjects must already possess the access rights to be delegated.
   *
   *                         Delegation policies are aimed at subjects delegating rights to servers or third parties to perform actions on their behalf and are not meant to be the means by which security administrators would assign rights to subjects. A negative delegation policy identifies what delegations are forbidden.
   *
   *                         A Delegation policy specifies the authorisation policy from which delegated rights are derived, the grantors, which are the entities which can delegate these access rights, and the grantees, which are the entities to which the access rights can be delegated. There are two types of delegation policy, positive and negative. (Based on PONDERS)
   */
  DelegationPolicy: v3_ActCode_DelegationPolicy,
  /**
   * Custodian security system must downgrade information assigned security labels by instantiating a new version of the classified information so as to break the binding of the classifying security label when assigning a new security label that marks the information as classified at a less protected level in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the previous assignment and binding.
   */
  DowngradeSecurityLabel: v3_ActCode_DowngradeSecurityLabel,
  /**
   * Custodian security system must assign and bind security labels derived from compilations of information by aggregation or disaggregation in order to classify information compiled in the information systems under its control for collection, access, use and disclosure in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the previous assignment and binding.
   */
  DeriveSecurityLabel: v3_ActCode_DeriveSecurityLabel,
  /**
   * Custodian system must render information unreadable by algorithmically transforming plaintext into ciphertext.
   *
   *
   *
   *
   *                            Usage Notes: A mathematical transposition of a file or data stream so that it cannot be deciphered at the receiving end without the proper key. Encryption is a security feature that assures that only the parties who are supposed to be participating in a videoconference or data transfer are able to do so. It can include a password, public and private keys, or a complex combination of all.  (Per Infoway.)
   */
  Encrypt: v3_ActCode_Encrypt,
  /**
   * Custodian system must render information unreadable and unusable by algorithmically transforming plaintext into ciphertext when "at rest" or in storage.
   */
  EncryptAtRest: v3_ActCode_EncryptAtRest,
  /**
   * Custodian system must render information unreadable and unusable by algorithmically transforming plaintext into ciphertext while "in transit" or being transported by any means.
   */
  EncryptInTransit: v3_ActCode_EncryptInTransit,
  /**
   * Custodian system must render information unreadable and unusable by algorithmically transforming plaintext into ciphertext while in use such that operations permitted on the target information are limited by the license granted to the end user.
   */
  EncryptInUse: v3_ActCode_EncryptInUse,
  /**
   * Custodian system must require human review and approval for permission requested.
   */
  HumanApproval: v3_ActCode_HumanApproval,
  /**
   * Custodian security system must assign and bind security labels in order to classify information created in the information systems under its control for collection, access, use and disclosure in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the assignment and binding.
   *
   *
   *                            Usage Note: In security systems, security policy label assignments do not change, they may supersede prior assignments, and such reassignments are always tracked for auditing and other purposes.
   */
  AssignSecurityLabel: v3_ActCode_AssignSecurityLabel,
  /**
   * Custodian system must render information unreadable and unusable by algorithmically transforming plaintext into ciphertext.  User may be provided a key to decrypt per license or "shared secret".
   */
  Mask: v3_ActCode_Mask,
  /**
   * Custodian must limit access and disclosure to the minimum information required to support an authorized user's purpose of use.
   *
   *
   *                            Usage Note: Limiting the information available for access and disclosure to that an authorized user or receiver "needs to know" in order to perform permitted workflow or purpose of use.
   */
  MinimumNecessary: v3_ActCode_MinimumNecessary,
  /**
   * Prohibition on disclosure without information subject's authorization.
   */
  NoDisclosureWithoutSubjectAuthorization: v3_ActCode_NoDisclosureWithoutSubjectAuthorization,
  /**
   * Prohibition on collection or storage of the information.
   */
  NoCollection: v3_ActCode_NoCollection,
  /**
   * Prohibition on disclosure without organizational approved patient restriction.
   */
  NoDisclosureWithoutConsentDirective: v3_ActCode_NoDisclosureWithoutConsentDirective,
  /**
   * Prohibition on disclosure without a consent directive from the information subject.
   */
  NoDisclosureWithoutInformationSubjectQuoteSConsentDirective: v3_ActCode_NoDisclosureWithoutInformationSubjectQuoteSConsentDirective,
  /**
   * Prohibition on Integration into other records.
   */
  NoIntegration: v3_ActCode_NoIntegration,
  /**
   * Prohibition on disclosure except to entities on specific access list.
   */
  NoUnlistedEntityDisclosure: v3_ActCode_NoUnlistedEntityDisclosure,
  /**
   * Prohibition on disclosure without an interagency service agreement or memorandum of understanding (MOU).
   */
  NoDisclosureWithoutMOU: v3_ActCode_NoDisclosureWithoutMOU,
  /**
   * Prohibition on disclosure without organizational authorization.
   */
  NoDisclosureWithoutOrganizationalAuthorization: v3_ActCode_NoDisclosureWithoutOrganizationalAuthorization,
  /**
   * Prohibition on disclosing information to patient, family or caregivers without attending provider's authorization.
   *
   *
   *                            Usage Note: The information may be labeled with the ActInformationSensitivity TBOO code, triggering application of this RefrainPolicy code as a handling caveat controlling access.
   *
   *                         Maps to FHIR NOPAT: Typically, this is used on an Alert resource, when the alert records information on patient abuse or non-compliance.
   *
   *                         FHIR print name is "keep information from patient". Maps to the French realm - code: INVISIBLE_PATIENT.
   *
   *
   *                            displayName: Document non visible par le patient
   *                            codingScheme: 1.2.250.1.213.1.1.4.13
   *
   *                         French use case:  A label for documents that the author  chose to hide from the patient until the content can be disclose to the patient in a face to face meeting between a healthcare professional and the patient (in French law some results like cancer diagnosis or AIDS diagnosis must be announced to the patient by a healthcare professional and should not be find out by the patient alone).
   */
  NoDisclosureToPatientFamilyOrCaregiversWithoutAttendingProviderQuoteSAuthorization: v3_ActCode_NoDisclosureToPatientFamilyOrCaregiversWithoutAttendingProviderQuoteSAuthorization,
  /**
   * Prohibition on collection of the information beyond time necessary to accomplish authorized purpose of use is prohibited.
   */
  NoCollectionBeyondPurposeOfUse: v3_ActCode_NoCollectionBeyondPurposeOfUse,
  /**
   * Prohibition on redisclosure without patient consent directive.
   */
  NoRedisclosureWithoutConsentDirective: v3_ActCode_NoRedisclosureWithoutConsentDirective,
  /**
   * Prohibition on redisclosure without a consent directive from the information subject.
   */
  NoRedisclosureWithoutInformationSubjectQuoteSConsentDirective: v3_ActCode_NoRedisclosureWithoutInformationSubjectQuoteSConsentDirective,
  /**
   * Prohibition on disclosure without authorization under jurisdictional law.
   */
  NoDisclosureWithoutJurisdictionalAuthorization: v3_ActCode_NoDisclosureWithoutJurisdictionalAuthorization,
  /**
   * Prohibition on associating de-identified or pseudonymized information with other information in a manner that could or does result in disclosing information intended to be masked.
   */
  NoRelinking: v3_ActCode_NoRelinking,
  /**
   * Prohibition on use of the information beyond the purpose of use initially authorized.
   */
  NoReuseBeyondPurposeOfUse: v3_ActCode_NoReuseBeyondPurposeOfUse,
  /**
   * Prohibition on disclosure except to principals with access permission to specific VIP information.
   */
  NoUnauthorizedVIPDisclosure: v3_ActCode_NoUnauthorizedVIPDisclosure,
  /**
   * Conveys the mandated workflow action that an information custodian, receiver, or user must perform.
   *
   *
   *                            Usage Notes: Per ISO 22600-2, ObligationPolicy instances 'are event-triggered and define actions to be performed by manager agent'. Per HL7 Composite Security and Privacy Domain Analysis Model:  This value set refers to the action required to receive the permission specified in the privacy rule. Per OASIS XACML, an obligation is an operation specified in a policy or policy that is performed in conjunction with the enforcement of an access control decision.
   */
  ObligationPolicy: v3_ActCode_ObligationPolicy,
  /**
   * Prohibition on disclosure except as permitted by the information originator.
   */
  NoDisclosureWithoutOriginatorAuthorization: v3_ActCode_NoDisclosureWithoutOriginatorAuthorization,
  /**
   * Custodian security system must persist the binding of security labels to classify information received or imported by information systems under its control for collection, access, use and disclosure in accordance with applicable jurisdictional privacy policies associated with the target information.  The system must retain an immutable record of the assignment and binding.
   */
  PersistSecurityLabel: v3_ActCode_PersistSecurityLabel,
  /**
   * Custodian must create and/or maintain human readable security label tags as required by policy.
   *
   *                         Map:  Aligns with ISO 22600-3 Section A.3.4.3 description of privacy mark:  "If present, the privacy-mark is not used for access control. The content of the privacy-mark may be defined by the security policy in force (identified by the security-policy-identifier) which may define a list of values to be used. Alternately, the value may be determined by the originator of the security-label."
   */
  PrivacyMark: v3_ActCode_PrivacyMark,
  /**
   * Custodian system must strip information of data that would allow the identification of the source of the information or the information subject.  Custodian may retain a key to relink data necessary to reidentify the information subject.
   */
  Pseudonymize: v3_ActCode_Pseudonymize,
  /**
   * Custodian system must remove information, which is not authorized to be access, used, or disclosed from records made available to otherwise authorized users.
   */
  Redact: v3_ActCode_Redact,
  /**
   * Conveys prohibited actions which an information custodian, receiver, or user is not permitted to perform unless otherwise authorized or permitted under specified circumstances.
   *
   *
   *
   *
   *                            Usage Notes: ISO 22600-2 species that a Refrain Policy "defines actions the subjects must refrain from performing".  Per HL7 Composite Security and Privacy Domain Analysis Model:  May be used to indicate that a specific action is prohibited based on specific access control attributes e.g., purpose of use, information type, user role, etc.
   */
  RefrainPolicy: v3_ActCode_RefrainPolicy,
  /**
   * Types of security policies that further specify the ActClassPolicy value set.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            obligation to encrypt
   *                            refrain from redisclosure without consent
   */
  SecurityPolicy: v3_ActCode_SecurityPolicy,
  /**
   * Custodian security system must declassify information assigned security labels by instantiating a new version of the classified information so as to break the binding of the classifying security label when assigning a new security label that marks the information as classified at a more protected level  in accordance with applicable jurisdictional privacy policies associated with the target information. The system must retain an immutable record of the previous assignment and binding.
   */
  UpgradeSecurityLabel: v3_ActCode_UpgradeSecurityLabel,
};
const v3_Sequencing_Ascending: Coding = {
  code: 'A',
  display: 'Ascending',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Sequencing',
};
const v3_Sequencing_Descending: Coding = {
  code: 'D',
  display: 'Descending',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Sequencing',
};
const v3_Sequencing_None: Coding = {
  code: 'N',
  display: 'None',
  system: 'http://terminology.hl7.org/CodeSystem/v3-Sequencing',
};
/**
 *  Specifies sequence of sort order.
 */
export const V3Sequencing = {
  /**
   * Ascending sequence order.
   */
  Ascending: v3_Sequencing_Ascending,
  /**
   * Descending sequence order.
   */
  Descending: v3_Sequencing_Descending,
  /**
   * No enforced sequence order.
   */
  None: v3_Sequencing_None,
};
/**
 *  A role of a place that further classifies the setting (e.g., accident site, road side, work site, community location) in which services are delivered.
 */
export const V3ServiceDeliveryLocationRoleType = {
  /**
   * A role of a place that further classifies the clinical setting (e.g., cardiology clinic, primary care clinic, rehabilitation hospital, skilled nursing facility) in which care is delivered during an encounter.
   */
  DedicatedClinicalLocationRoleType: v3_RoleCode_DedicatedClinicalLocationRoleType,
  /**
   * A role of a place that further classifies a setting that is intended to house the provision of non-clinical services.
   */
  DedicatedNonClinicalLocationRoleType: v3_RoleCode_DedicatedNonClinicalLocationRoleType,
  /**
   * A role of a place that further classifies a setting that is intended to house the provision of services.
   */
  DedicatedServiceDeliveryLocationRoleType: v3_RoleCode_DedicatedServiceDeliveryLocationRoleType,
  /**
   * IncidentalServiceDeliveryLocationRoleType
   */
  IncidentalServiceDeliveryLocationRoleType: v3_RoleCode_IncidentalServiceDeliveryLocationRoleType,
  /**
   * Location of an accident where healthcare service was delivered, such as a roadside.
   */
  AccidentSite: v3_RoleCode_AccidentSite,
  /**
   * Allergy clinic
   */
  AllergyClinic: v3_RoleCode_AllergyClinic,
  /**
   * Location (mobile) where healthcare service was delivered, identified specifically as an ambulance.
   */
  Ambulance: v3_RoleCode_Ambulance,
  /**
   * Amputee clinic
   */
  AmputeeClinic: v3_RoleCode_AmputeeClinic,
  /**
   * Bone marrow transplant clinic
   */
  BoneMarrowTransplantClinic: v3_RoleCode_BoneMarrowTransplantClinic,
  /**
   * Bone marrow transplant unit
   */
  BoneMarrowTransplantUnit: v3_RoleCode_BoneMarrowTransplantUnit,
  /**
   * Breast clinic
   */
  BreastClinic: v3_RoleCode_BreastClinic,
  /**
   * Child and adolescent neurology clinic
   */
  ChildAndAdolescentNeurologyClinic: v3_RoleCode_ChildAndAdolescentNeurologyClinic,
  /**
   * Child and adolescent psychiatry clinic
   */
  ChildAndAdolescentPsychiatryClinic: v3_RoleCode_ChildAndAdolescentPsychiatryClinic,
  /**
   * Ambulatory Health Care Facilities; Clinic/Center; Rehabilitation: Cardiac Facilities
   */
  AmbulatoryHealthCareFacilitiesClinicCenterRehabilitationCardiacFacilities: v3_RoleCode_AmbulatoryHealthCareFacilitiesClinicCenterRehabilitationCardiacFacilities,
  /**
   * Cardiac catheterization lab
   */
  CardiacCatheterizationLab: v3_RoleCode_CardiacCatheterizationLab,
  /**
   * Coronary care unit
   */
  CoronaryCareUnit: v3_RoleCode_CoronaryCareUnit,
  /**
   * A specialty unit in hospital that focuses on chronic respirator patients and pulmonary failure
   */
  ChestUnit: v3_RoleCode_ChestUnit,
  /**
   * (1) A hospital including a physical plant and personnel that provides multidisciplinary diagnosis and treatment for diseases that have one or more of the following characteristics: is permanent; leaves residual disability; is caused by nonreversible pathological alteration; requires special training of the patient for rehabilitation; and/or may be expected to require a long period of supervision or care. In addition, patients require the safety, security, and shelter of these specialized inpatient or partial hospitalization settings. (2) A hospital that provides medical and skilled nursing services to patients with long-term illnesses who are not in an acute phase but who require an intensity of services not available in nursing homes
   */
  ChronicCareFacility: v3_RoleCode_ChronicCareFacility,
  /**
   * Coagulation clinic
   */
  CoagulationClinic: v3_RoleCode_CoagulationClinic,
  /**
   * Community location where healthcare is delivered.
   */
  CommunityLocation: v3_RoleCode_CommunityLocation,
  /**
   * Colon and rectal surgery clinic
   */
  ColonAndRectalSurgeryClinic: v3_RoleCode_ColonAndRectalSurgeryClinic,
  /**
   * Description: A location that plays the role of delivering services which may include providing front-line services to the population of a defined geographic area such as: healthcare services and social services, preventive or curative, rehabilitation or reintegration.
   */
  CommunityServiceCenter: v3_RoleCode_CommunityServiceCenter,
  /**
   * A practice setting where cardiovascular diagnostic procedures or therapeutic interventions are performed (e.g., cardiac catheterization lab, echocardiography suite)
   */
  CardiovascularDiagnosticsOrTherapeuticsUnit: v3_RoleCode_CardiovascularDiagnosticsOrTherapeuticsUnit,
  /**
   * Location address where medical supplies were transported to for use.
   */
  DeliveryAddress: v3_RoleCode_DeliveryAddress,
  /**
   * Dermatology clinic
   */
  DermatologyClinic: v3_RoleCode_DermatologyClinic,
  /**
   * A practice setting where diagnostic procedures or therapeutic interventions are performed
   */
  DiagnosticsOrTherapeuticsUnit: v3_RoleCode_DiagnosticsOrTherapeuticsUnit,
  /**
   * Echocardiography lab
   */
  EchocardiographyLab: v3_RoleCode_EchocardiographyLab,
  /**
   * Endocrinology clinic
   */
  EndocrinologyClinic: v3_RoleCode_EndocrinologyClinic,
  /**
   * (X12N 261QD0000N)
   */
  EndoscopyLab: v3_RoleCode_EndoscopyLab,
  /**
   * Otorhinolaryngology clinic
   */
  OtorhinolaryngologyClinic: v3_RoleCode_OtorhinolaryngologyClinic,
  /**
   * Epilepsy unit
   */
  EpilepsyUnit: v3_RoleCode_EpilepsyUnit,
  /**
   * The section of a health care facility for providing rapid treatment to victims of sudden illness or trauma.
   */
  EmergencyRoom: v3_RoleCode_EmergencyRoom,
  /**
   * Emergency trauma unit
   */
  EmergencyTraumaUnit: v3_RoleCode_EmergencyTraumaUnit,
  /**
   * Family medicine clinic
   */
  FamilyMedicineClinic: v3_RoleCode_FamilyMedicineClinic,
  /**
   * (X12N 282N00000N)
   */
  HospitalsGeneralAcuteCareHospital: v3_RoleCode_HospitalsGeneralAcuteCareHospital,
  /**
   * Gastroenterology clinic
   */
  GastroenterologyClinic: v3_RoleCode_GastroenterologyClinic,
  /**
   * A practice setting where GI procedures (such as endoscopies) are performed
   */
  GastroenterologyDiagnosticsOrTherapeuticsLab: v3_RoleCode_GastroenterologyDiagnosticsOrTherapeuticsLab,
  /**
   * General internal medicine clinic
   */
  GeneralInternalMedicineClinic: v3_RoleCode_GeneralInternalMedicineClinic,
  /**
   * Gynecology clinic
   */
  GynecologyClinic: v3_RoleCode_GynecologyClinic,
  /**
   * Hand clinic
   */
  HandClinic: v3_RoleCode_HandClinic,
  /**
   * Hemodialysis unit
   */
  HemodialysisUnit: v3_RoleCode_HemodialysisUnit,
  /**
   * Hematology clinic
   */
  HematologyClinic: v3_RoleCode_HematologyClinic,
  /**
   * Description: A location that plays the role of delivering services which may include tests done based on clinical specimens to get health information about a patient as pertaining to the diagnosis, treatment and prevention of disease.  Hospital laboratories may be further divided into specialized units such as Anatomic Pathology, Microbiology, and Biochemistry.
   */
  HospitalLaboratory: v3_RoleCode_HospitalLaboratory,
  /**
   * An acute care institution that provides medical, surgical, or psychiatric care and treatment for the sick or the injured.
   */
  Hospital: v3_RoleCode_Hospital,
  /**
   * Description: A location that plays the role of delivering services which may include the branch of medicine that uses ionizing and non-ionizing radiation to diagnose and treat diseases.  The radiology unit may be further divided into subspecialties such as Imaging, Cardiovascular, Thoracic, and Ultrasound.
   */
  RadiologyUnit: v3_RoleCode_RadiologyUnit,
  /**
   * Hypertension clinic
   */
  HypertensionClinic: v3_RoleCode_HypertensionClinic,
  /**
   * Hospital unit
   */
  HospitalUnit: v3_RoleCode_HospitalUnit,
  /**
   * Description: A location that plays the role of delivering services which may include collecting specimens and/or samples from patients for laboratory testing purposes, but does not perform any tests or analysis functions.
   */
  SpecimenCollectionSite: v3_RoleCode_SpecimenCollectionSite,
  /**
   * Intensive care unit
   */
  IntensiveCareUnit: v3_RoleCode_IntensiveCareUnit,
  /**
   * Focuses on assessing disability
   */
  ImpairmentEvaluationCenter: v3_RoleCode_ImpairmentEvaluationCenter,
  /**
   * Infectious disease clinic
   */
  InfectiousDiseaseClinic: v3_RoleCode_InfectiousDiseaseClinic,
  /**
   * Description: A location that plays the role of delivering services which may include tests are done on clinical specimens to get health information about a patient pertaining to the diagnosis, treatment, and prevention of disease for a hospital visit longer than one day.
   */
  InpatientLaboratory: v3_RoleCode_InpatientLaboratory,
  /**
   * Description: A location that plays the role of delivering services which may include providing judicious, safe, efficacious, appropriate and cost effective use of medicines for treatment of patients for visits longer than one day. The distinction between inpatient pharmacies and retail (or outpatient) pharmacies is that they are part of a patient's continuity of care while staying in the hospital.
   */
  InpatientPharmacy: v3_RoleCode_InpatientPharmacy,
  /**
   * Infertility clinic
   */
  InfertilityClinic: v3_RoleCode_InfertilityClinic,
  /**
   * Lympedema clinic
   */
  LympedemaClinic: v3_RoleCode_LympedemaClinic,
  /**
   * Description: A location that plays the role of delivering services which include biochemistry, hematology, microbiology, immunochemistry, and toxicology.
   */
  MedicalLaboratory: v3_RoleCode_MedicalLaboratory,
  /**
   * Medical genetics clinic
   */
  MedicalGeneticsClinic: v3_RoleCode_MedicalGeneticsClinic,
  /**
   * A health care facility operated by the Department of Defense or other military operation.
   */
  MilitaryHospital: v3_RoleCode_MilitaryHospital,
  /**
   * Location (mobile) where healthcare service was delivered.
   */
  MobileUnit: v3_RoleCode_MobileUnit,
  /**
   * Nursing or custodial care facility
   */
  NursingOrCustodialCareFacility: v3_RoleCode_NursingOrCustodialCareFacility,
  /**
   * Neurology critical care and stroke unit
   */
  NeurologyCriticalCareAndStrokeUnit: v3_RoleCode_NeurologyCriticalCareAndStrokeUnit,
  /**
   * Nephrology clinic
   */
  NephrologyClinic: v3_RoleCode_NephrologyClinic,
  /**
   * Neurology clinic
   */
  NeurologyClinic: v3_RoleCode_NeurologyClinic,
  /**
   * Neurosurgery unit
   */
  NeurosurgeryUnit: v3_RoleCode_NeurosurgeryUnit,
  /**
   * Obstetrics clinic
   */
  ObstetricsClinic: v3_RoleCode_ObstetricsClinic,
  /**
   * Outpatient facility
   */
  OutpatientFacility: v3_RoleCode_OutpatientFacility,
  /**
   * Oral and maxillofacial surgery clinic
   */
  OralAndMaxillofacialSurgeryClinic: v3_RoleCode_OralAndMaxillofacialSurgeryClinic,
  /**
   * Medical oncology clinic
   */
  MedicalOncologyClinic: v3_RoleCode_MedicalOncologyClinic,
  /**
   * Opthalmology clinic
   */
  OpthalmologyClinic: v3_RoleCode_OpthalmologyClinic,
  /**
   * Description: A location that plays the role of delivering services which may include examination, diagnosis, treatment, management, and prevention of diseases and disorders of the eye as well as prescribing and fitting appropriate corrective lenses (glasses or contact lenses) as needed.  Optometry clinics may also provide tests for visual field screening, measuring intra-ocular pressure and ophthalmoscopy, as and when required.
   */
  OptometryClinic: v3_RoleCode_OptometryClinic,
  /**
   * Orthopedics clinic
   */
  OrthopedicsClinic: v3_RoleCode_OrthopedicsClinic,
  /**
   * Description: A location that plays the role of delivering services which may include tests are done on clinical specimens to get health information about a patient pertaining to the diagnosis, treatment, and prevention of disease for same day visits.
   */
  OutpatientLaboratory: v3_RoleCode_OutpatientLaboratory,
  /**
   * Description: A location that plays the role of delivering services which may include providing judicious, safe, efficacious, appropriate and cost effective use of medicines for treatment of patients for outpatient visits and may also be used for discharge prescriptions.
   */
  OutpatientPharmacy: v3_RoleCode_OutpatientPharmacy,
  /**
   * (X12N 261QP3300N)
   */
  PainClinic: v3_RoleCode_PainClinic,
  /**
   * (X12N 261QP2300N)
   */
  PrimaryCareClinic: v3_RoleCode_PrimaryCareClinic,
  /**
   * Pediatrics clinic
   */
  PediatricsClinic: v3_RoleCode_PediatricsClinic,
  /**
   * Pediatric cardiology clinic
   */
  PediatricCardiologyClinic: v3_RoleCode_PediatricCardiologyClinic,
  /**
   * Pediatric endocrinology clinic
   */
  PediatricEndocrinologyClinic: v3_RoleCode_PediatricEndocrinologyClinic,
  /**
   * Pediatric gastroenterology clinic
   */
  PediatricGastroenterologyClinic: v3_RoleCode_PediatricGastroenterologyClinic,
  /**
   * Pediatric hematology clinic
   */
  PediatricHematologyClinic: v3_RoleCode_PediatricHematologyClinic,
  /**
   * Pediatric oncology clinic
   */
  PediatricOncologyClinic: v3_RoleCode_PediatricOncologyClinic,
  /**
   * Pediatric intensive care unit
   */
  PediatricIntensiveCareUnit: v3_RoleCode_PediatricIntensiveCareUnit,
  /**
   * Pediatric infectious disease clinic
   */
  PediatricInfectiousDiseaseClinic: v3_RoleCode_PediatricInfectiousDiseaseClinic,
  /**
   * Pediatric nephrology clinic
   */
  PediatricNephrologyClinic: v3_RoleCode_PediatricNephrologyClinic,
  /**
   * Pediatric neonatal intensive care unit
   */
  PediatricNeonatalIntensiveCareUnit: v3_RoleCode_PediatricNeonatalIntensiveCareUnit,
  /**
   * Pediatric rheumatology clinic
   */
  PediatricRheumatologyClinic: v3_RoleCode_PediatricRheumatologyClinic,
  /**
   * Pediatric unit
   */
  PediatricUnit: v3_RoleCode_PediatricUnit,
  /**
   * Location where healthcare service was delivered, identified as a pharmacy.
   */
  Pharmacy: v3_RoleCode_Pharmacy,
  /**
   * (X12N 273R00000N)
   */
  PsychiatricHospitalUnit: v3_RoleCode_PsychiatricHospitalUnit,
  /**
   * Plastic surgery clinic
   */
  PlasticSurgeryClinic: v3_RoleCode_PlasticSurgeryClinic,
  /**
   * (X12N 261QP1100N)
   */
  PodiatryClinic: v3_RoleCode_PodiatryClinic,
  /**
   * Pain rehabilitation center
   */
  PainRehabilitationCenter: v3_RoleCode_PainRehabilitationCenter,
  /**
   * Preventive medicine clinic
   */
  PreventiveMedicineClinic: v3_RoleCode_PreventiveMedicineClinic,
  /**
   * Proctology clinic
   */
  ProctologyClinic: v3_RoleCode_ProctologyClinic,
  /**
   * Location where healthcare service was delivered, identified as the healthcare provider's practice office.
   */
  ProviderQuoteSOffice: v3_RoleCode_ProviderQuoteSOffice,
  /**
   * Prosthodontics clinic
   */
  ProsthodonticsClinic: v3_RoleCode_ProsthodonticsClinic,
  /**
   * Psychology clinic
   */
  PsychologyClinic: v3_RoleCode_PsychologyClinic,
  /**
   * Psychiatry clinic
   */
  PsychiatryClinic: v3_RoleCode_PsychiatryClinic,
  /**
   * Healthcare facility that cares for patients with psychiatric illness(s).
   */
  PsychatricCareFacility: v3_RoleCode_PsychatricCareFacility,
  /**
   * location where healthcare was delivered which is the residence of the Patient.
   */
  PatientQuoteSResidence: v3_RoleCode_PatientQuoteSResidence,
  /**
   * A practice setting where radiology services (diagnostic or therapeutic) are provided            (X12N 261QR0200N)
   */
  RadiologyDiagnosticsOrTherapeuticsUnit: v3_RoleCode_RadiologyDiagnosticsOrTherapeuticsUnit,
  /**
   * (X12N 261QX0203N)
   */
  RadiationOncologyUnit: v3_RoleCode_RadiationOncologyUnit,
  /**
   * (X12N 283X00000N)
   */
  RehabilitationHospital: v3_RoleCode_RehabilitationHospital,
  /**
   * Description: A location that plays the role of delivering services which may include life training and/or social support to people with addictions.
   */
  AddictionTreatmentCenter: v3_RoleCode_AddictionTreatmentCenter,
  /**
   * Rheumatology clinic
   */
  RheumatologyClinic: v3_RoleCode_RheumatologyClinic,
  /**
   * Description: A location that plays the role of delivering services which may include adaptation, rehabilitation and social integration services for people with intellectual and/or pervasive development disorders such as autism or severe behaviour disorder.
   */
  IntellectualImpairmentCenter: v3_RoleCode_IntellectualImpairmentCenter,
  /**
   * Description: A location that plays the role of delivering services which may social support services for adolescents who are pregnant or have child and are experiencing adaptation issues/difficulties in their current or eventual parenting role.
   */
  ParentsWithAdjustmentDifficultiesCenter: v3_RoleCode_ParentsWithAdjustmentDifficultiesCenter,
  /**
   * Description: A location that plays the role of delivering services which may include adaptation, rehabilitation and social integration services for people with physical impairments.
   */
  PhysicalImpairmentCenter: v3_RoleCode_PhysicalImpairmentCenter,
  /**
   * Description: A location that plays the role of delivering services for people with hearing impairments.
   */
  PhysicalImpairmentHearingCenter: v3_RoleCode_PhysicalImpairmentHearingCenter,
  /**
   * Description: A location that plays the role of delivering services for people with motor skill impairments.
   */
  PhysicalImpairmentMotorSkillsCenter: v3_RoleCode_PhysicalImpairmentMotorSkillsCenter,
  /**
   * Description: A location that plays the role of delivering services for people with visual skill impairments.
   */
  PhysicalImpairmentVisualSkillsCenter: v3_RoleCode_PhysicalImpairmentVisualSkillsCenter,
  /**
   * Rehabilitation hospital unit
   */
  RehabilitationHospitalUnit: v3_RoleCode_RehabilitationHospitalUnit,
  /**
   * Description: A location that plays the role of delivering services which may include life training and/or social support services for the adaption, rehabilitation and social integration of youths with adjustment difficulties.
   */
  YouthsWithAdjustmentDifficultiesCenter: v3_RoleCode_YouthsWithAdjustmentDifficultiesCenter,
  /**
   * Neuroradiology unit
   */
  NeuroradiologyUnit: v3_RoleCode_NeuroradiologyUnit,
  /**
   * Residential treatment facility
   */
  ResidentialTreatmentFacility: v3_RoleCode_ResidentialTreatmentFacility,
  /**
   * Location where healthcare service was delivered, identified as a school or educational facility.
   */
  School: v3_RoleCode_School,
  /**
   * (X12N 261QA1200N)
   */
  SleepDisordersUnit: v3_RoleCode_SleepDisordersUnit,
  /**
   * (X12N 314000000N)
   */
  SkilledNursingFacility: v3_RoleCode_SkilledNursingFacility,
  /**
   * Sports medicine clinic
   */
  SportsMedicineClinic: v3_RoleCode_SportsMedicineClinic,
  /**
   * Surgery clinic
   */
  SurgeryClinic: v3_RoleCode_SurgeryClinic,
  /**
   * (X12N 324500000N)
   */
  SubstanceUseRehabilitationFacility: v3_RoleCode_SubstanceUseRehabilitationFacility,
  /**
   * Transplant clinic
   */
  TransplantClinic: v3_RoleCode_TransplantClinic,
  /**
   * Travel and geographic medicine clinic
   */
  TravelAndGeographicMedicineClinic: v3_RoleCode_TravelAndGeographicMedicineClinic,
  /**
   * Description: A location that plays the role of delivering services which may include: social emergency services required for a young person as required under any jurisdictional youth laws, child placement, and family mediation in the defined geographical area the SDL is responsible for. It may provide expertise in a judiciary setting on child custody, adoption and biological history research.
   */
  UnderageProtectionCenter: v3_RoleCode_UnderageProtectionCenter,
  /**
   * Urology clinic
   */
  UrologyClinic: v3_RoleCode_UrologyClinic,
  /**
   * Wound clinic
   */
  WoundClinic: v3_RoleCode_WoundClinic,
  /**
   * Location where healthcare service was delivered, identified as a work place.
   */
  WorkSite: v3_RoleCode_WorkSite,
};
const v3_SetOperator_ValueSetOperator: Coding = {
  code: '_ValueSetOperator',
  display: 'ValueSetOperator',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SetOperator',
};
const v3_SetOperator_Intersect: Coding = {
  code: 'A',
  display: 'intersect',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SetOperator',
};
const v3_SetOperator_Exclude: Coding = {
  code: 'E',
  display: 'exclude',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SetOperator',
};
const v3_SetOperator_ConvexHull: Coding = {
  code: 'H',
  display: 'convex hull',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SetOperator',
};
const v3_SetOperator_Include: Coding = {
  code: 'I',
  display: 'include',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SetOperator',
};
const v3_SetOperator_PeriodicHull: Coding = {
  code: 'P',
  display: 'periodic hull',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SetOperator',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3SetOperator = {
  /**
   * Operations that can be used to associate concepts in a terminology.
   */
  ValueSetOperator: v3_SetOperator_ValueSetOperator,
  /**
   * Form the intersection with the value.
   */
  Intersect: v3_SetOperator_Intersect,
  /**
   * Form the set-difference with this value, i.e., exclude this element or set from the resulting set.
   */
  Exclude: v3_SetOperator_Exclude,
  /**
   * Form the convex hull with the value. The convex hull is defined over ordered domains and is the smallest contiguous superset (interval) that of all the operand sets.
   */
  ConvexHull: v3_SetOperator_ConvexHull,
  /**
   * Form the union with this value, i.e., include this element or set in the resulting set.
   */
  Include: v3_SetOperator_Include,
  /**
   * Form the periodic hull with the value. The periodic hull is defined over ordered domains and is the periodic set that contains all contiguous supersets of pairs of intervals generated by the operand periodic intervals.
   */
  PeriodicHull: v3_SetOperator_PeriodicHull,
};
/**
 *  Potential values for observations of severity.
 */
export const V3SeverityObservation = {
  /**
   * Indicates the condition may be life-threatening or has the potential to cause permanent injury.
   */
  High: v3_ObservationValue_High,
  /**
   * Indicates the condition may result in some adverse consequences but is unlikely to substantially affect the situation of the subject.
   */
  Low: v3_ObservationValue_Low,
  /**
   * Indicates the condition may result in noticable adverse adverse consequences but is unlikely to be life-threatening or cause permanent injury.
   */
  Moderate: v3_ObservationValue_Moderate,
};
const v3_SpecimenType_SpecimenEntityType: Coding = {
  code: '_SpecimenEntityType',
  display: 'SpecimenEntityType',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Abcess: Coding = {
  code: 'ABS',
  display: 'Abcess',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_AmnioticFluid: Coding = {
  code: 'AMN',
  display: 'Amniotic fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Aspirate: Coding = {
  code: 'ASP',
  display: 'Aspirate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_BloodBag: Coding = {
  code: 'BBL',
  display: 'Blood bag',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_WholeBody: Coding = {
  code: 'BDY',
  display: 'Whole body',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_BileFluid: Coding = {
  code: 'BIFL',
  display: 'Bile fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_WholeBlood: Coding = {
  code: 'BLD',
  display: 'Whole blood',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_BloodArterial: Coding = {
  code: 'BLDA',
  display: 'Blood arterial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_BloodCapillary: Coding = {
  code: 'BLDC',
  display: 'Blood capillary',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_BloodCord: Coding = {
  code: 'BLDCO',
  display: 'Blood - cord',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_BloodVenous: Coding = {
  code: 'BLDV',
  display: 'Blood venous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Bone: Coding = {
  code: 'BON',
  display: 'Bone',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Basophils: Coding = {
  code: 'BPH',
  display: 'Basophils',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_BloodProductUnit: Coding = {
  code: 'BPU',
  display: 'Blood product unit',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Burn: Coding = {
  code: 'BRN',
  display: 'Burn',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Bronchial: Coding = {
  code: 'BRO',
  display: 'Bronchial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_ExhaledGasEqualsBreath: Coding = {
  code: 'BRTH',
  display: 'Exhaled gas (=breath)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_CalculusEqualsStone: Coding = {
  code: 'CALC',
  display: 'Calculus (=Stone)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_CardiacMuscle: Coding = {
  code: 'CDM',
  display: 'Cardiac muscle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Conjunctiva: Coding = {
  code: 'CNJT',
  display: 'Conjunctiva',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Cannula: Coding = {
  code: 'CNL',
  display: 'Cannula',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Colostrum: Coding = {
  code: 'COL',
  display: 'Colostrum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Cornea: Coding = {
  code: 'CRN',
  display: 'Cornea',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_CerebralSpinalFluid: Coding = {
  code: 'CSF',
  display: 'Cerebral spinal fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_CatheterTip: Coding = {
  code: 'CTP',
  display: 'Catheter tip',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Curettage: Coding = {
  code: 'CUR',
  display: 'Curettage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_CervicalMucus: Coding = {
  code: 'CVM',
  display: 'Cervical mucus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Cervix: Coding = {
  code: 'CVX',
  display: 'Cervix',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Cyst: Coding = {
  code: 'CYST',
  display: 'Cyst',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_DialysisFluid: Coding = {
  code: 'DIAF',
  display: 'Dialysis fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_DoseMedOrSubstance: Coding = {
  code: 'DOSE',
  display: 'Dose med or substance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Drain: Coding = {
  code: 'DRN',
  display: 'Drain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_DuodenalFluid: Coding = {
  code: 'DUFL',
  display: 'Duodenal fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Ear: Coding = {
  code: 'EAR',
  display: 'Ear',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_EarWaxCerumen: Coding = {
  code: 'EARW',
  display: 'Ear wax (cerumen)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Electrode: Coding = {
  code: 'ELT',
  display: 'Electrode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Endocardium: Coding = {
  code: 'ENDC',
  display: 'Endocardium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Endometrium: Coding = {
  code: 'ENDM',
  display: 'Endometrium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Eosinophils: Coding = {
  code: 'EOS',
  display: 'Eosinophils',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Eye: Coding = {
  code: 'EYE',
  display: 'Eye',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Fibroblasts: Coding = {
  code: 'FIB',
  display: 'Fibroblasts',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Fistula: Coding = {
  code: 'FIST',
  display: 'Fistula',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Filter: Coding = {
  code: 'FLT',
  display: 'Filter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_BodyFluidUnsp: Coding = {
  code: 'FLU',
  display: 'Body fluid, unsp',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_FoodSample: Coding = {
  code: 'FOOD',
  display: 'Food sample',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Gas: Coding = {
  code: 'GAS',
  display: 'Gas',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_GastricFluidContents: Coding = {
  code: 'GAST',
  display: 'Gastric fluid/contents',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Genital: Coding = {
  code: 'GEN',
  display: 'Genital',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_GenitalCervix: Coding = {
  code: 'GENC',
  display: 'Genital cervix',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_GenitalFluid: Coding = {
  code: 'GENF',
  display: 'Genital fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_GenitalLochia: Coding = {
  code: 'GENL',
  display: 'Genital lochia',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_GenitalVaginal: Coding = {
  code: 'GENV',
  display: 'Genital vaginal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Hair: Coding = {
  code: 'HAR',
  display: 'Hair',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_InhaledGas: Coding = {
  code: 'IHG',
  display: 'Inhaled Gas',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Isolate: Coding = {
  code: 'ISLT',
  display: 'Isolate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_IntubationTube: Coding = {
  code: 'IT',
  display: 'Intubation tube',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Lamella: Coding = {
  code: 'LAM',
  display: 'Lamella',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_LiquidNOS: Coding = {
  code: 'LIQ',
  display: 'Liquid NOS',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Line: Coding = {
  code: 'LN',
  display: 'Line',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_LineArterial: Coding = {
  code: 'LNA',
  display: 'Line arterial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_LineVenous: Coding = {
  code: 'LNV',
  display: 'Line venous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Lymphocytes: Coding = {
  code: 'LYM',
  display: 'Lymphocytes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Macrophages: Coding = {
  code: 'MAC',
  display: 'Macrophages',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_MarrowBone: Coding = {
  code: 'MAR',
  display: 'Marrow (bone)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_MenstrualBlood: Coding = {
  code: 'MBLD',
  display: 'Menstrual blood',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Meconium: Coding = {
  code: 'MEC',
  display: 'Meconium',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_BreastMilk: Coding = {
  code: 'MILK',
  display: 'Breast milk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Milk: Coding = {
  code: 'MLK',
  display: 'Milk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Nail: Coding = {
  code: 'NAIL',
  display: 'Nail',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_NoseNasalPassage: Coding = {
  code: 'NOS',
  display: 'Nose (nasal passage)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_PancreaticFluid: Coding = {
  code: 'PAFL',
  display: 'Pancreatic fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Patient: Coding = {
  code: 'PAT',
  display: 'Patient',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Plasma: Coding = {
  code: 'PLAS',
  display: 'Plasma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_PlasmaBag: Coding = {
  code: 'PLB',
  display: 'Plasma bag',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Placenta: Coding = {
  code: 'PLC',
  display: 'Placenta',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_PleuralFluidThoracentesisFld: Coding = {
  code: 'PLR',
  display: 'Pleural fluid (thoracentesis fld)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_PolymorphonuclearNeutrophils: Coding = {
  code: 'PMN',
  display: 'Polymorphonuclear neutrophils',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_PlateletPoorPlasma: Coding = {
  code: 'PPP',
  display: 'Platelet poor plasma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_PlateletRichPlasma: Coding = {
  code: 'PRP',
  display: 'Platelet rich plasma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_PeritonealFluidAscites: Coding = {
  code: 'PRT',
  display: 'Peritoneal fluid /ascites',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Pus: Coding = {
  code: 'PUS',
  display: 'Pus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Erythrocytes: Coding = {
  code: 'RBC',
  display: 'Erythrocytes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Saliva: Coding = {
  code: 'SAL',
  display: 'Saliva',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Serum: Coding = {
  code: 'SER',
  display: 'Serum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_SkeletalMuscle: Coding = {
  code: 'SKM',
  display: 'Skeletal muscle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Skin: Coding = {
  code: 'SKN',
  display: 'Skin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_SeminalFluid: Coding = {
  code: 'SMN',
  display: 'Seminal fluid',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_SeminalPlasma: Coding = {
  code: 'SMPLS',
  display: 'Seminal plasma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_SynovialFluidJointFluid: Coding = {
  code: 'SNV',
  display: 'Synovial fluid (Joint fluid)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Spermatozoa: Coding = {
  code: 'SPRM',
  display: 'Spermatozoa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Sputum: Coding = {
  code: 'SPT',
  display: 'Sputum',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_SputumCoughed: Coding = {
  code: 'SPTC',
  display: 'Sputum - coughed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_SputumTrachealAspirate: Coding = {
  code: 'SPTT',
  display: 'Sputum - tracheal aspirate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_StoolEqualsFecal: Coding = {
  code: 'STL',
  display: 'Stool = Fecal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Sweat: Coding = {
  code: 'SWT',
  display: 'Sweat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Tears: Coding = {
  code: 'TEAR',
  display: 'Tears',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_ThrombocytePlatelet: Coding = {
  code: 'THRB',
  display: 'Thrombocyte (platelet)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Throat: Coding = {
  code: 'THRT',
  display: 'Throat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_TissueGallBladder: Coding = {
  code: 'TISG',
  display: 'Tissue gall bladder',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_TissuePlacenta: Coding = {
  code: 'TISPL',
  display: 'Tissue placenta',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_TissueUnspecified: Coding = {
  code: 'TISS',
  display: 'Tissue, unspecified',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_TissueUlcer: Coding = {
  code: 'TISU',
  display: 'Tissue ulcer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_TissueLargeIntestine: Coding = {
  code: 'TLGI',
  display: 'Tissue large intestine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_TissueLung: Coding = {
  code: 'TLNG',
  display: 'Tissue lung',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_TissueSmallIntestineTissueUlcer: Coding = {
  code: 'TSMI',
  display: 'Tissue small intestine Tissue ulcer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_TubeUnspecified: Coding = {
  code: 'TUB',
  display: 'Tube, unspecified',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Ulcer: Coding = {
  code: 'ULC',
  display: 'Ulcer',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_UmbilicalBlood: Coding = {
  code: 'UMB',
  display: 'Umbilical blood',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_UnknownMedicine: Coding = {
  code: 'UMED',
  display: 'Unknown medicine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Urine: Coding = {
  code: 'UR',
  display: 'Urine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_UrineCleanCatch: Coding = {
  code: 'URC',
  display: 'Urine clean catch',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_UrineSediment: Coding = {
  code: 'URNS',
  display: 'Urine sediment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_UrineCatheter: Coding = {
  code: 'URT',
  display: 'Urine catheter',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Urethra: Coding = {
  code: 'URTH',
  display: 'Urethra',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_UnknownSubstance: Coding = {
  code: 'USUB',
  display: 'Unknown substance',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Vomitus: Coding = {
  code: 'VOM',
  display: 'Vomitus',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Water: Coding = {
  code: 'WAT',
  display: 'Water',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Leukocytes: Coding = {
  code: 'WBC',
  display: 'Leukocytes',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Wick: Coding = {
  code: 'WICK',
  display: 'Wick',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_Wound: Coding = {
  code: 'WND',
  display: 'Wound',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_WoundAbscess: Coding = {
  code: 'WNDA',
  display: 'Wound abscess',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_WoundDrainage: Coding = {
  code: 'WNDD',
  display: 'Wound drainage',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
const v3_SpecimenType_WoundExudate: Coding = {
  code: 'WNDE',
  display: 'Wound exudate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SpecimenType',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3SpecimenType = {
  /**
   * SpecimenEntityType
   */
  SpecimenEntityType: v3_SpecimenType_SpecimenEntityType,
  /**
   * Abcess
   */
  Abcess: v3_SpecimenType_Abcess,
  /**
   * Amniotic fluid
   */
  AmnioticFluid: v3_SpecimenType_AmnioticFluid,
  /**
   * Aspirate
   */
  Aspirate: v3_SpecimenType_Aspirate,
  /**
   * Blood bag
   */
  BloodBag: v3_SpecimenType_BloodBag,
  /**
   * Whole body
   */
  WholeBody: v3_SpecimenType_WholeBody,
  /**
   * Bile fluid
   */
  BileFluid: v3_SpecimenType_BileFluid,
  /**
   * Whole blood
   */
  WholeBlood: v3_SpecimenType_WholeBlood,
  /**
   * Blood arterial
   */
  BloodArterial: v3_SpecimenType_BloodArterial,
  /**
   * Blood capillary
   */
  BloodCapillary: v3_SpecimenType_BloodCapillary,
  /**
   * Blood - cord
   */
  BloodCord: v3_SpecimenType_BloodCord,
  /**
   * Blood venous
   */
  BloodVenous: v3_SpecimenType_BloodVenous,
  /**
   * Bone
   */
  Bone: v3_SpecimenType_Bone,
  /**
   * Basophils
   */
  Basophils: v3_SpecimenType_Basophils,
  /**
   * Blood product unit
   */
  BloodProductUnit: v3_SpecimenType_BloodProductUnit,
  /**
   * Burn
   */
  Burn: v3_SpecimenType_Burn,
  /**
   * Bronchial
   */
  Bronchial: v3_SpecimenType_Bronchial,
  /**
   * Exhaled gas (=breath)
   */
  ExhaledGasEqualsBreath: v3_SpecimenType_ExhaledGasEqualsBreath,
  /**
   * Calculus (=Stone)
   */
  CalculusEqualsStone: v3_SpecimenType_CalculusEqualsStone,
  /**
   * Cardiac muscle
   */
  CardiacMuscle: v3_SpecimenType_CardiacMuscle,
  /**
   * Conjunctiva
   */
  Conjunctiva: v3_SpecimenType_Conjunctiva,
  /**
   * Cannula
   */
  Cannula: v3_SpecimenType_Cannula,
  /**
   * Colostrum
   */
  Colostrum: v3_SpecimenType_Colostrum,
  /**
   * Cornea
   */
  Cornea: v3_SpecimenType_Cornea,
  /**
   * Cerebral spinal fluid
   */
  CerebralSpinalFluid: v3_SpecimenType_CerebralSpinalFluid,
  /**
   * Catheter tip
   */
  CatheterTip: v3_SpecimenType_CatheterTip,
  /**
   * Curettage
   */
  Curettage: v3_SpecimenType_Curettage,
  /**
   * Cervical mucus
   */
  CervicalMucus: v3_SpecimenType_CervicalMucus,
  /**
   * Cervix
   */
  Cervix: v3_SpecimenType_Cervix,
  /**
   * Cyst
   */
  Cyst: v3_SpecimenType_Cyst,
  /**
   * Dialysis fluid
   */
  DialysisFluid: v3_SpecimenType_DialysisFluid,
  /**
   * Dose med or substance
   */
  DoseMedOrSubstance: v3_SpecimenType_DoseMedOrSubstance,
  /**
   * Drain
   */
  Drain: v3_SpecimenType_Drain,
  /**
   * Duodenal fluid
   */
  DuodenalFluid: v3_SpecimenType_DuodenalFluid,
  /**
   * Ear
   */
  Ear: v3_SpecimenType_Ear,
  /**
   * Ear wax (cerumen)
   */
  EarWaxCerumen: v3_SpecimenType_EarWaxCerumen,
  /**
   * Electrode
   */
  Electrode: v3_SpecimenType_Electrode,
  /**
   * Endocardium
   */
  Endocardium: v3_SpecimenType_Endocardium,
  /**
   * Endometrium
   */
  Endometrium: v3_SpecimenType_Endometrium,
  /**
   * Eosinophils
   */
  Eosinophils: v3_SpecimenType_Eosinophils,
  /**
   * Eye
   */
  Eye: v3_SpecimenType_Eye,
  /**
   * Fibroblasts
   */
  Fibroblasts: v3_SpecimenType_Fibroblasts,
  /**
   * Fistula
   */
  Fistula: v3_SpecimenType_Fistula,
  /**
   * Filter
   */
  Filter: v3_SpecimenType_Filter,
  /**
   * Body fluid, unsp
   */
  BodyFluidUnsp: v3_SpecimenType_BodyFluidUnsp,
  /**
   * Food sample
   */
  FoodSample: v3_SpecimenType_FoodSample,
  /**
   * Gas
   */
  Gas: v3_SpecimenType_Gas,
  /**
   * Gastric fluid/contents
   */
  GastricFluidContents: v3_SpecimenType_GastricFluidContents,
  /**
   * Genital
   */
  Genital: v3_SpecimenType_Genital,
  /**
   * Genital cervix
   */
  GenitalCervix: v3_SpecimenType_GenitalCervix,
  /**
   * Genital fluid
   */
  GenitalFluid: v3_SpecimenType_GenitalFluid,
  /**
   * Genital lochia
   */
  GenitalLochia: v3_SpecimenType_GenitalLochia,
  /**
   * Genital vaginal
   */
  GenitalVaginal: v3_SpecimenType_GenitalVaginal,
  /**
   * Hair
   */
  Hair: v3_SpecimenType_Hair,
  /**
   * Inhaled Gas
   */
  InhaledGas: v3_SpecimenType_InhaledGas,
  /**
   * Isolate
   */
  Isolate: v3_SpecimenType_Isolate,
  /**
   * Intubation tube
   */
  IntubationTube: v3_SpecimenType_IntubationTube,
  /**
   * Lamella
   */
  Lamella: v3_SpecimenType_Lamella,
  /**
   * Liquid NOS
   */
  LiquidNOS: v3_SpecimenType_LiquidNOS,
  /**
   * Line
   */
  Line: v3_SpecimenType_Line,
  /**
   * Line arterial
   */
  LineArterial: v3_SpecimenType_LineArterial,
  /**
   * Line venous
   */
  LineVenous: v3_SpecimenType_LineVenous,
  /**
   * Lymphocytes
   */
  Lymphocytes: v3_SpecimenType_Lymphocytes,
  /**
   * Macrophages
   */
  Macrophages: v3_SpecimenType_Macrophages,
  /**
   * Marrow (bone)
   */
  MarrowBone: v3_SpecimenType_MarrowBone,
  /**
   * Menstrual blood
   */
  MenstrualBlood: v3_SpecimenType_MenstrualBlood,
  /**
   * Meconium
   */
  Meconium: v3_SpecimenType_Meconium,
  /**
   * Breast milk
   */
  BreastMilk: v3_SpecimenType_BreastMilk,
  /**
   * Milk
   */
  Milk: v3_SpecimenType_Milk,
  /**
   * Nail
   */
  Nail: v3_SpecimenType_Nail,
  /**
   * Nose (nasal passage)
   */
  NoseNasalPassage: v3_SpecimenType_NoseNasalPassage,
  /**
   * Pancreatic fluid
   */
  PancreaticFluid: v3_SpecimenType_PancreaticFluid,
  /**
   * Patient
   */
  Patient: v3_SpecimenType_Patient,
  /**
   * Plasma
   */
  Plasma: v3_SpecimenType_Plasma,
  /**
   * Plasma bag
   */
  PlasmaBag: v3_SpecimenType_PlasmaBag,
  /**
   * Placenta
   */
  Placenta: v3_SpecimenType_Placenta,
  /**
   * Pleural fluid (thoracentesis fld)
   */
  PleuralFluidThoracentesisFld: v3_SpecimenType_PleuralFluidThoracentesisFld,
  /**
   * Polymorphonuclear neutrophils
   */
  PolymorphonuclearNeutrophils: v3_SpecimenType_PolymorphonuclearNeutrophils,
  /**
   * Platelet poor plasma
   */
  PlateletPoorPlasma: v3_SpecimenType_PlateletPoorPlasma,
  /**
   * Platelet rich plasma
   */
  PlateletRichPlasma: v3_SpecimenType_PlateletRichPlasma,
  /**
   * Peritoneal fluid /ascites
   */
  PeritonealFluidAscites: v3_SpecimenType_PeritonealFluidAscites,
  /**
   * Pus
   */
  Pus: v3_SpecimenType_Pus,
  /**
   * Erythrocytes
   */
  Erythrocytes: v3_SpecimenType_Erythrocytes,
  /**
   * Saliva
   */
  Saliva: v3_SpecimenType_Saliva,
  /**
   * Serum
   */
  Serum: v3_SpecimenType_Serum,
  /**
   * Skeletal muscle
   */
  SkeletalMuscle: v3_SpecimenType_SkeletalMuscle,
  /**
   * Skin
   */
  Skin: v3_SpecimenType_Skin,
  /**
   * Seminal fluid
   */
  SeminalFluid: v3_SpecimenType_SeminalFluid,
  /**
   * Seminal plasma
   */
  SeminalPlasma: v3_SpecimenType_SeminalPlasma,
  /**
   * Synovial fluid (Joint fluid)
   */
  SynovialFluidJointFluid: v3_SpecimenType_SynovialFluidJointFluid,
  /**
   * Spermatozoa
   */
  Spermatozoa: v3_SpecimenType_Spermatozoa,
  /**
   * Sputum
   */
  Sputum: v3_SpecimenType_Sputum,
  /**
   * Sputum - coughed
   */
  SputumCoughed: v3_SpecimenType_SputumCoughed,
  /**
   * Sputum - tracheal aspirate
   */
  SputumTrachealAspirate: v3_SpecimenType_SputumTrachealAspirate,
  /**
   * Stool = Fecal
   */
  StoolEqualsFecal: v3_SpecimenType_StoolEqualsFecal,
  /**
   * Sweat
   */
  Sweat: v3_SpecimenType_Sweat,
  /**
   * Tears
   */
  Tears: v3_SpecimenType_Tears,
  /**
   * Thrombocyte (platelet)
   */
  ThrombocytePlatelet: v3_SpecimenType_ThrombocytePlatelet,
  /**
   * Throat
   */
  Throat: v3_SpecimenType_Throat,
  /**
   * Tissue gall bladder
   */
  TissueGallBladder: v3_SpecimenType_TissueGallBladder,
  /**
   * Tissue placenta
   */
  TissuePlacenta: v3_SpecimenType_TissuePlacenta,
  /**
   * Tissue, unspecified
   */
  TissueUnspecified: v3_SpecimenType_TissueUnspecified,
  /**
   * Tissue ulcer
   */
  TissueUlcer: v3_SpecimenType_TissueUlcer,
  /**
   * Tissue large intestine
   */
  TissueLargeIntestine: v3_SpecimenType_TissueLargeIntestine,
  /**
   * Tissue lung
   */
  TissueLung: v3_SpecimenType_TissueLung,
  /**
   * Tissue small intestine Tissue ulcer
   */
  TissueSmallIntestineTissueUlcer: v3_SpecimenType_TissueSmallIntestineTissueUlcer,
  /**
   * Tube, unspecified
   */
  TubeUnspecified: v3_SpecimenType_TubeUnspecified,
  /**
   * Ulcer
   */
  Ulcer: v3_SpecimenType_Ulcer,
  /**
   * Umbilical blood
   */
  UmbilicalBlood: v3_SpecimenType_UmbilicalBlood,
  /**
   * Unknown medicine
   */
  UnknownMedicine: v3_SpecimenType_UnknownMedicine,
  /**
   * Urine
   */
  Urine: v3_SpecimenType_Urine,
  /**
   * Urine clean catch
   */
  UrineCleanCatch: v3_SpecimenType_UrineCleanCatch,
  /**
   * Urine sediment
   */
  UrineSediment: v3_SpecimenType_UrineSediment,
  /**
   * Urine catheter
   */
  UrineCatheter: v3_SpecimenType_UrineCatheter,
  /**
   * Urethra
   */
  Urethra: v3_SpecimenType_Urethra,
  /**
   * Unknown substance
   */
  UnknownSubstance: v3_SpecimenType_UnknownSubstance,
  /**
   * Vomitus
   */
  Vomitus: v3_SpecimenType_Vomitus,
  /**
   * Water
   */
  Water: v3_SpecimenType_Water,
  /**
   * Leukocytes
   */
  Leukocytes: v3_SpecimenType_Leukocytes,
  /**
   * Wick
   */
  Wick: v3_SpecimenType_Wick,
  /**
   * Wound
   */
  Wound: v3_SpecimenType_Wound,
  /**
   * Wound abscess
   */
  WoundAbscess: v3_SpecimenType_WoundAbscess,
  /**
   * Wound drainage
   */
  WoundDrainage: v3_SpecimenType_WoundDrainage,
  /**
   * Wound exudate
   */
  WoundExudate: v3_SpecimenType_WoundExudate,
};
const v3_styleType_FontStyle: Coding = {
  code: '_FontStyle',
  display: 'Font Style',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_ListStyle: Coding = {
  code: '_ListStyle',
  display: 'List Style',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_OrderedListStyle: Coding = {
  code: '_OrderedListStyle',
  display: 'Ordered List Style',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_TableRuleStyle: Coding = {
  code: '_TableRuleStyle',
  display: 'Table Rule Style',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_UnorderedListStyle: Coding = {
  code: '_UnorderedListStyle',
  display: 'Unordered List Style',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_Arabic: Coding = {
  code: 'Arabic',
  display: 'Arabic',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_BigAlpha: Coding = {
  code: 'BigAlpha',
  display: 'Big Alpha',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_BigRoman: Coding = {
  code: 'BigRoman',
  display: 'Big Roman',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_BoldFont: Coding = {
  code: 'bold',
  display: 'Bold Font',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_BottomRule: Coding = {
  code: 'Botrule',
  display: 'Bottom Rule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_Circle: Coding = {
  code: 'Circle',
  display: 'Circle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_Disc: Coding = {
  code: 'Disc',
  display: 'Disc',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_EmphasisedFont: Coding = {
  code: 'emphasis',
  display: 'Emphasised Font',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_ItalicsFont: Coding = {
  code: 'italics',
  display: 'Italics Font',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_LittleAlpha: Coding = {
  code: 'LittleAlpha',
  display: 'Little Alpha',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_LittleRoman: Coding = {
  code: 'LittleRoman',
  display: 'Little Roman',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_LeftSidedRule: Coding = {
  code: 'Lrule',
  display: 'Left-sided rule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_RightSidedRule: Coding = {
  code: 'Rrule',
  display: 'Right-sided rule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_Square: Coding = {
  code: 'Square',
  display: 'Square',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_TopRule: Coding = {
  code: 'Toprule',
  display: 'Top Rule',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
const v3_styleType_UnderlineFont: Coding = {
  code: 'underline',
  display: 'Underline Font',
  system: 'http://terminology.hl7.org/CodeSystem/v3-styleType',
};
/**
 *  <ns1:p>The style code is used within the CDA/SPL narrative block to give the instance author some control over various aspects of style</ns1:p>
 */
export const V3StyleType = {
  /**
   * Defines font rendering characteristics
   */
  FontStyle: v3_styleType_FontStyle,
  /**
   * Defines list rendering characteristics
   */
  ListStyle: v3_styleType_ListStyle,
  /**
   * Defines rendering characteristics for ordered lists
   */
  OrderedListStyle: v3_styleType_OrderedListStyle,
  /**
   * Defines table cell rendering characteristics
   */
  TableRuleStyle: v3_styleType_TableRuleStyle,
  /**
   * Defines rendering characteristics for unordered lists
   */
  UnorderedListStyle: v3_styleType_UnorderedListStyle,
  /**
   * List is ordered using Arabic numerals: 1, 2, 3
   */
  Arabic: v3_styleType_Arabic,
  /**
   * List is ordered using big alpha characters: A, B, C
   */
  BigAlpha: v3_styleType_BigAlpha,
  /**
   * List is ordered using big Roman numerals: I, II, III
   */
  BigRoman: v3_styleType_BigRoman,
  /**
   * Render with a bold font
   */
  BoldFont: v3_styleType_BoldFont,
  /**
   * Render cell with rule on bottom
   */
  BottomRule: v3_styleType_BottomRule,
  /**
   * List bullets are hollow discs
   */
  Circle: v3_styleType_Circle,
  /**
   * List bullets are simple solid discs
   */
  Disc: v3_styleType_Disc,
  /**
   * Render with with some type of emphasis
   */
  EmphasisedFont: v3_styleType_EmphasisedFont,
  /**
   * Render italicized
   */
  ItalicsFont: v3_styleType_ItalicsFont,
  /**
   * List is order using little alpha characters: a, b, c
   */
  LittleAlpha: v3_styleType_LittleAlpha,
  /**
   * List is ordered using little Roman numerals: i, ii, iii
   */
  LittleRoman: v3_styleType_LittleRoman,
  /**
   * Render cell with left-sided rule
   */
  LeftSidedRule: v3_styleType_LeftSidedRule,
  /**
   * Render cell with right-sided rule
   */
  RightSidedRule: v3_styleType_RightSidedRule,
  /**
   * List bullets are solid squares
   */
  Square: v3_styleType_Square,
  /**
   * Render cell with rule on top
   */
  TopRule: v3_styleType_TopRule,
  /**
   * Render with an underline font
   */
  UnderlineFont: v3_styleType_UnderlineFont,
};
const v3_substanceAdminSubstitution_ActSubstanceAdminSubstitutionCode: Coding = {
  code: '_ActSubstanceAdminSubstitutionCode',
  display: 'ActSubstanceAdminSubstitutionCode',
  system: 'http://terminology.hl7.org/CodeSystem/v3-substanceAdminSubstitution',
};
/**
 *  Identifies what sort of change is permitted or has occurred between the therapy that was ordered and the therapy that was/will be provided.
 */
export const V3SubstanceAdminSubstitution = {
  /**
   * Description: Substitution occurred or is permitted with another product that may potentially have different ingredients, but having the same biological and therapeutic effects.
   */
  ActSubstanceAdminSubstitutionCode: v3_substanceAdminSubstitution_ActSubstanceAdminSubstitutionCode,
  /**
   * Description:
   *
   *
   *                         Substitution occurred or is permitted between equivalent Brands but not Generics
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Zestril  for Prinivil
   *                            Coumadin for Jantoven
   */
  BrandComposition: v3_substanceAdminSubstitution_BrandComposition,
  /**
   * Description: Substitution occurred or is permitted with another bioequivalent and therapeutically equivalent product.
   */
  Equivalent: v3_substanceAdminSubstitution_Equivalent,
  /**
   * Description:
   *
   *
   *                         Substitution occurred or is permitted with another product that is a:
   *
   *
   *                            pharmaceutical alternative containing the same active ingredient but is formulated with different salt, ester
   *                            pharmaceutical equivalent that has the same active ingredient, strength, dosage form and route of administration
   *
   *
   *                            Examples:
   *
   *
   *
   *
   *                               Pharmaceutical alternative: Erythromycin Ethylsuccinate for Erythromycin Stearate
   *
   *                               Pharmaceutical equivalent: Lisonpril for Zestril
   */
  EquivalentComposition: v3_substanceAdminSubstitution_EquivalentComposition,
  /**
   * Description: This substitution was performed or is permitted based on formulary guidelines.
   */
  Formulary: v3_substanceAdminSubstitution_Formulary,
  /**
   * Description: Substitution occurred or is permitted between equivalent Generics but not Brands
   *
   *
   *                            Examples:
   *
   *
   *
   *                            Lisnopril (Lupin Corp) for Lisnopril (Wockhardt Corp)
   */
  GenericComposition: v3_substanceAdminSubstitution_GenericComposition,
  /**
   * No substitution occurred or is permitted.
   */
  None: v3_substanceAdminSubstitution_None,
  /**
   * Description: Substitution occurred or is permitted between therapeutically equivalent Brands but not Generics
   * >
   *                            Examples:
   *
   *
   *
   *                            Zantac for Tagamet
   */
  TherapeuticBrand: v3_substanceAdminSubstitution_TherapeuticBrand,
  /**
   * Description: Substitution occurred or is permitted with another product having the same therapeutic objective and safety profile.
   *
   *
   *                            Examples:
   *
   *
   *
   *                            ranitidine for Tagamet
   */
  TherapeuticAlternative: v3_substanceAdminSubstitution_TherapeuticAlternative,
  /**
   * Description: Substitution occurred or is permitted between therapeutically equivalent Generics but not Brands
   * >
   *                            Examples:
   *
   *
   *
   *                            Ranitidine  for cimetidine
   */
  TherapeuticGeneric: v3_substanceAdminSubstitution_TherapeuticGeneric,
};
/**
 * No Description Provided
 */
export const V3SubstanceAdminSubstitutionReason = {
  /**
   * Indicates that the decision to substitute or to not substitute was driven by a desire to maintain consistency with a pre-existing therapy.  I.e. The performer provided the same item/service as had been previously provided rather than providing exactly what was ordered, or rather than substituting with a lower-cost equivalent.
   */
  ContinuingTherapy: v3_ActReason_ContinuingTherapy,
  /**
   * Indicates that the decision to substitute or to not substitute was driven by a policy expressed within the formulary.
   */
  FormularyPolicy: v3_ActReason_FormularyPolicy,
  /**
   * In the case of 'substitution', indicates that the substitution occurred because the ordered item was not in stock.  In the case of 'no substitution', indicates that a cheaper equivalent was not substituted because it was not in stock.
   */
  OutOfStock: v3_ActReason_OutOfStock,
  /**
   * Indicates that the decision to substitute or to not substitute was driven by a jurisdictional regulatory requirement mandating or prohibiting substitution.
   */
  RegulatoryRequirement: v3_ActReason_RegulatoryRequirement,
};
const v3_SubstitutionCondition_Conditional: Coding = {
  code: '_Conditional',
  display: 'Conditional',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SubstitutionCondition',
};
const v3_SubstitutionCondition_ConfirmFirst: Coding = {
  code: 'CONFIRM',
  display: 'Confirm first',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SubstitutionCondition',
};
const v3_SubstitutionCondition_NoSubstitution: Coding = {
  code: 'NOSUB',
  display: 'No substitution',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SubstitutionCondition',
};
const v3_SubstitutionCondition_NotifyFirst: Coding = {
  code: 'NOTIFY',
  display: 'Notify first',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SubstitutionCondition',
};
const v3_SubstitutionCondition_Unconditional: Coding = {
  code: 'UNCOND',
  display: 'Unconditional',
  system: 'http://terminology.hl7.org/CodeSystem/v3-SubstitutionCondition',
};
/**
 *  Identifies what sort of change is permitted or has occurred between the item that was ordered/requested and the one that was/will be provided.
 */
export const V3SubstitutionCondition = {
  /**
   * Some conditions may be attached to an allowable substitution.  An allowable substitution is based on a match to any other attributes that may be specified.
   */
  Conditional: v3_SubstitutionCondition_Conditional,
  /**
   * Confirmation with Contact Person prior to making any substitutions has or will occur.
   */
  ConfirmFirst: v3_SubstitutionCondition_ConfirmFirst,
  /**
   * Substitution is not permitted.
   */
  NoSubstitution: v3_SubstitutionCondition_NoSubstitution,
  /**
   * Notification to the Contact Person, prior to substitution and through normal institutional procedures, has or will be made.
   */
  NotifyFirst: v3_SubstitutionCondition_NotifyFirst,
  /**
   * No conditions are required.
   */
  Unconditional: v3_SubstitutionCondition_Unconditional,
};
const v3_TableCellHorizontalAlign_Center: Coding = {
  code: 'center',
  display: 'center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableCellHorizontalAlign',
};
const v3_TableCellHorizontalAlign_Char: Coding = {
  code: 'char',
  display: 'char',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableCellHorizontalAlign',
};
const v3_TableCellHorizontalAlign_Justify: Coding = {
  code: 'justify',
  display: 'justify',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableCellHorizontalAlign',
};
const v3_TableCellHorizontalAlign_Left: Coding = {
  code: 'left',
  display: 'left',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableCellHorizontalAlign',
};
const v3_TableCellHorizontalAlign_Right: Coding = {
  code: 'right',
  display: 'right',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableCellHorizontalAlign',
};
/**
 *  These values are defined within the XHTML 4.0 Table Model
 */
export const V3TableCellHorizontalAlign = {
  /**
   * center
   */
  Center: v3_TableCellHorizontalAlign_Center,
  /**
   * char
   */
  Char: v3_TableCellHorizontalAlign_Char,
  /**
   * justify
   */
  Justify: v3_TableCellHorizontalAlign_Justify,
  /**
   * left
   */
  Left: v3_TableCellHorizontalAlign_Left,
  /**
   * right
   */
  Right: v3_TableCellHorizontalAlign_Right,
};
const v3_TableCellScope_Col: Coding = {
  code: 'col',
  display: 'col',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableCellScope',
};
const v3_TableCellScope_Colgroup: Coding = {
  code: 'colgroup',
  display: 'colgroup',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableCellScope',
};
const v3_TableCellScope_Row: Coding = {
  code: 'row',
  display: 'row',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableCellScope',
};
const v3_TableCellScope_Rowgroup: Coding = {
  code: 'rowgroup',
  display: 'rowgroup',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableCellScope',
};
/**
 *  These values are defined within the XHTML 4.0 Table Model
 */
export const V3TableCellScope = {
  /**
   * col
   */
  Col: v3_TableCellScope_Col,
  /**
   * colgroup
   */
  Colgroup: v3_TableCellScope_Colgroup,
  /**
   * row
   */
  Row: v3_TableCellScope_Row,
  /**
   * rowgroup
   */
  Rowgroup: v3_TableCellScope_Rowgroup,
};
const v3_TableCellVerticalAlign_Baseline: Coding = {
  code: 'baseline',
  display: 'baseline',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableCellVerticalAlign',
};
const v3_TableCellVerticalAlign_Bottom: Coding = {
  code: 'bottom',
  display: 'bottom',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableCellVerticalAlign',
};
const v3_TableCellVerticalAlign_Middle: Coding = {
  code: 'middle',
  display: 'middle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableCellVerticalAlign',
};
const v3_TableCellVerticalAlign_Top: Coding = {
  code: 'top',
  display: 'top',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableCellVerticalAlign',
};
/**
 *  These values are defined within the XHTML 4.0 Table Model
 */
export const V3TableCellVerticalAlign = {
  /**
   * baseline
   */
  Baseline: v3_TableCellVerticalAlign_Baseline,
  /**
   * bottom
   */
  Bottom: v3_TableCellVerticalAlign_Bottom,
  /**
   * middle
   */
  Middle: v3_TableCellVerticalAlign_Middle,
  /**
   * top
   */
  Top: v3_TableCellVerticalAlign_Top,
};
const v3_TableFrame_Above: Coding = {
  code: 'above',
  display: 'above',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableFrame',
};
const v3_TableFrame_Below: Coding = {
  code: 'below',
  display: 'below',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableFrame',
};
const v3_TableFrame_Border: Coding = {
  code: 'border',
  display: 'border',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableFrame',
};
const v3_TableFrame_Box: Coding = {
  code: 'box',
  display: 'box',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableFrame',
};
const v3_TableFrame_Hsides: Coding = {
  code: 'hsides',
  display: 'hsides',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableFrame',
};
const v3_TableFrame_Lhs: Coding = {
  code: 'lhs',
  display: 'lhs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableFrame',
};
const v3_TableFrame_Rhs: Coding = {
  code: 'rhs',
  display: 'rhs',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableFrame',
};
const v3_TableFrame_Void: Coding = {
  code: 'void',
  display: 'void',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableFrame',
};
const v3_TableFrame_Vsides: Coding = {
  code: 'vsides',
  display: 'vsides',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableFrame',
};
/**
 *  These values are defined within the XHTML 4.0 Table Model
 */
export const V3TableFrame = {
  /**
   * above
   */
  Above: v3_TableFrame_Above,
  /**
   * below
   */
  Below: v3_TableFrame_Below,
  /**
   * border
   */
  Border: v3_TableFrame_Border,
  /**
   * box
   */
  Box: v3_TableFrame_Box,
  /**
   * hsides
   */
  Hsides: v3_TableFrame_Hsides,
  /**
   * lhs
   */
  Lhs: v3_TableFrame_Lhs,
  /**
   * rhs
   */
  Rhs: v3_TableFrame_Rhs,
  /**
   * void
   */
  Void: v3_TableFrame_Void,
  /**
   * vsides
   */
  Vsides: v3_TableFrame_Vsides,
};
const v3_TableRules_All: Coding = {
  code: 'all',
  display: 'all',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableRules',
};
const v3_TableRules_Cols: Coding = {
  code: 'cols',
  display: 'cols',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableRules',
};
const v3_TableRules_Groups: Coding = {
  code: 'groups',
  display: 'groups',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableRules',
};
const v3_TableRules_None: Coding = {
  code: 'none',
  display: 'none',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableRules',
};
const v3_TableRules_Rows: Coding = {
  code: 'rows',
  display: 'rows',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TableRules',
};
/**
 *  These values are defined within the XHTML 4.0 Table Model
 */
export const V3TableRules = {
  /**
   * all
   */
  All: v3_TableRules_All,
  /**
   * cols
   */
  Cols: v3_TableRules_Cols,
  /**
   * groups
   */
  Groups: v3_TableRules_Groups,
  /**
   * none
   */
  None: v3_TableRules_None,
  /**
   * rows
   */
  Rows: v3_TableRules_Rows,
};
const v3_TargetAwareness_Denying: Coding = {
  code: 'D',
  display: 'denying',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TargetAwareness',
};
const v3_TargetAwareness_FullAwareness: Coding = {
  code: 'F',
  display: 'full awareness',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TargetAwareness',
};
const v3_TargetAwareness_Incapable: Coding = {
  code: 'I',
  display: 'incapable',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TargetAwareness',
};
const v3_TargetAwareness_Marginal: Coding = {
  code: 'M',
  display: 'marginal',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TargetAwareness',
};
const v3_TargetAwareness_Partial: Coding = {
  code: 'P',
  display: 'partial',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TargetAwareness',
};
const v3_TargetAwareness_Uninformed: Coding = {
  code: 'U',
  display: 'uninformed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TargetAwareness',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3TargetAwareness = {
  /**
   * Target person has been informed about the issue but currently denies it.
   */
  Denying: v3_TargetAwareness_Denying,
  /**
   * Target person is fully aware of the issue.
   */
  FullAwareness: v3_TargetAwareness_FullAwareness,
  /**
   * Target person is not capable of comprehending the issue.
   */
  Incapable: v3_TargetAwareness_Incapable,
  /**
   * Target person is marginally aware of the issue.
   */
  Marginal: v3_TargetAwareness_Marginal,
  /**
   * Target person is partially aware of the issue.
   */
  Partial: v3_TargetAwareness_Partial,
  /**
   * Target person has not yet been informed of the issue.
   */
  Uninformed: v3_TargetAwareness_Uninformed,
};
const v3_TelecommunicationCapabilities_Data: Coding = {
  code: 'data',
  display: 'data',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TelecommunicationCapabilities',
};
const v3_TelecommunicationCapabilities_Fax: Coding = {
  code: 'fax',
  display: 'fax',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TelecommunicationCapabilities',
};
const v3_TelecommunicationCapabilities_Sms: Coding = {
  code: 'sms',
  display: 'sms',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TelecommunicationCapabilities',
};
const v3_TelecommunicationCapabilities_Text: Coding = {
  code: 'tty',
  display: 'text',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TelecommunicationCapabilities',
};
const v3_TelecommunicationCapabilities_Voice: Coding = {
  code: 'voice',
  display: 'voice',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TelecommunicationCapabilities',
};
/**
 *   Description:
 * Concepts that define the telecommunication capabilities of a particular device. Used to identify the expected capabilities to be found at a particular telecommunication address.
 */
export const V3TelecommunicationCapabilities = {
  /**
   * Description: This device can receive data calls (i.e. modem).
   */
  Data: v3_TelecommunicationCapabilities_Data,
  /**
   * Description: This device can receive faxes.
   */
  Fax: v3_TelecommunicationCapabilities_Fax,
  /**
   * Description: This device can receive SMS messages.
   */
  Sms: v3_TelecommunicationCapabilities_Sms,
  /**
   * Description: This device is a text telephone.
   */
  Text: v3_TelecommunicationCapabilities_Text,
  /**
   * Description: This device can receive voice calls (i.e. talking to another person, or a recording device, or a voice activated computer).
   */
  Voice: v3_TelecommunicationCapabilities_Voice,
};
const v3_TimingEvent_IC: Coding = {
  code: 'IC',
  display: 'IC',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const v3_TimingEvent_ICD: Coding = {
  code: 'ICD',
  display: 'ICD',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const v3_TimingEvent_ICM: Coding = {
  code: 'ICM',
  display: 'ICM',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
const v3_TimingEvent_ICV: Coding = {
  code: 'ICV',
  display: 'ICV',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TimingEvent',
};
/**
 * **** MISSING DEFINITIONS ****
 */
export const V3TimingEvent = {
  /**
   * before meal (from lat. ante cibus)
   */
  AC: v3_TimingEvent_AC,
  /**
   * before lunch (from lat. ante cibus diurnus)
   */
  ACD: v3_TimingEvent_ACD,
  /**
   * before breakfast (from lat. ante cibus matutinus)
   */
  ACM: v3_TimingEvent_ACM,
  /**
   * before dinner (from lat. ante cibus vespertinus)
   */
  ACV: v3_TimingEvent_ACV,
  /**
   * Description: meal (from lat. ante cibus)
   */
  C: v3_TimingEvent_C,
  /**
   * Description: lunch (from lat. cibus diurnus)
   */
  CD: v3_TimingEvent_CD,
  /**
   * Description: breakfast (from lat. cibus matutinus)
   */
  CM: v3_TimingEvent_CM,
  /**
   * Description: dinner (from lat. cibus vespertinus)
   */
  CV: v3_TimingEvent_CV,
  /**
   * Description: Prior to beginning a regular period of extended sleep (this would exclude naps).  Note that this might occur at different times of day depending on a person's regular sleep schedule.
   */
  HS: v3_TimingEvent_HS,
  /**
   * between meals (from lat. inter cibus)
   */
  IC: v3_TimingEvent_IC,
  /**
   * between lunch and dinner
   */
  ICD: v3_TimingEvent_ICD,
  /**
   * between breakfast and lunch
   */
  ICM: v3_TimingEvent_ICM,
  /**
   * between dinner and the hour of sleep
   */
  ICV: v3_TimingEvent_ICV,
  /**
   * after meal (from lat. post cibus)
   */
  PC: v3_TimingEvent_PC,
  /**
   * after lunch (from lat. post cibus diurnus)
   */
  PCD: v3_TimingEvent_PCD,
  /**
   * after breakfast (from lat. post cibus matutinus)
   */
  PCM: v3_TimingEvent_PCM,
  /**
   * after dinner (from lat. post cibus vespertinus)
   */
  PCV: v3_TimingEvent_PCV,
  /**
   * Description: Upon waking up from a regular period of sleep, in order to start regular activities (this would exclude waking up from a nap or temporarily waking up during a period of sleep)
   *
   *
   *                            Usage Notes: e.g.
   *
   *                         Take pulse rate on waking in management of thyrotoxicosis.
   *
   *                         Take BP on waking in management of hypertension
   *
   *                         Take basal body temperature on waking in establishing date of ovulation
   */
  WAKE: v3_TimingEvent_WAKE,
};
const v3_TransmissionRelationshipTypeCode_Sequence: Coding = {
  code: 'SEQL',
  display: 'sequence',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TransmissionRelationshipTypeCode',
};
/**
 *   Description:
 * A code specifying the meaning and purpose of every TransmissionRelationship instance. Each of its values implies specific constraints to what kinds of Transmission objects can be related and in which way.
 */
export const V3TransmissionRelationshipTypeCode = {
  /**
   * Description:A transmission relationship indicating that the source transmission follows the target transmission.
   */
  Sequence: v3_TransmissionRelationshipTypeCode_Sequence,
};
const v3_TribalEntityUS_NativeEntityAlaska: Coding = {
  code: '_NativeEntityAlaska',
  display: 'NativeEntityAlaska',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeEntityContiguous: Coding = {
  code: '_NativeEntityContiguous',
  display: 'NativeEntityContiguous',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AbsenteeShawneeTribeOfIndiansOfOklahoma: Coding = {
  code: '1',
  display: 'Absentee-Shawnee Tribe of Indians of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AssiniboineAndSiouxTribesOfTheFortPeckIndi: Coding = {
  code: '10',
  display: 'Assiniboine and Sioux Tribes of the Fort Peck Indi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HavasupaiTribeOfTheHavasupaiReservationAriz: Coding = {
  code: '100',
  display: 'Havasupai Tribe of the Havasupai Reservation, Ariz',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HoChunkNationOfWisconsinFormerlyKnownAsTh: Coding = {
  code: '101',
  display: 'Ho-Chunk Nation of Wisconsin (formerly known as th',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HohIndianTribeOfTheHohIndianReservationWa: Coding = {
  code: '102',
  display: 'Hoh Indian Tribe of the Hoh Indian Reservation, Wa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HoopaValleyTribeCalifornia: Coding = {
  code: '103',
  display: 'Hoopa Valley Tribe, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HopiTribeOfArizona: Coding = {
  code: '104',
  display: 'Hopi Tribe of Arizona',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HoplandBandOfPomoIndiansOfTheHoplandRanche: Coding = {
  code: '105',
  display: 'Hopland Band of Pomo Indians of the Hopland Ranche',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HoultonBandOfMaliseetIndiansOfMaine: Coding = {
  code: '106',
  display: 'Houlton Band of Maliseet Indians of Maine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HualapaiIndianTribeOfTheHualapaiIndianReser: Coding = {
  code: '107',
  display: 'Hualapai Indian Tribe of the Hualapai Indian Reser',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HuronPotawatomiIncMichigan: Coding = {
  code: '108',
  display: 'Huron Potawatomi, Inc., Michigan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_InajaBandOfDieguenoMissionIndiansOfTheInaj: Coding = {
  code: '109',
  display: 'Inaja Band of Diegueno Mission Indians of the Inaj',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AugustineBandOfCahuillaMissionIndiansOfThe: Coding = {
  code: '11',
  display: 'Augustine Band of Cahuilla Mission Indians of the',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_IoneBandOfMiwokIndiansOfCalifornia: Coding = {
  code: '110',
  display: 'Ione Band of Miwok Indians of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_IowaTribeOfKansasAndNebraska: Coding = {
  code: '111',
  display: 'Iowa Tribe of Kansas and Nebraska',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_IowaTribeOfOklahoma: Coding = {
  code: '112',
  display: 'Iowa Tribe of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_JacksonRancheriaOfMeWukIndiansOfCalifornia: Coding = {
  code: '113',
  display: 'Jackson Rancheria of Me-Wuk Indians of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_JamestownSQuoteKlallamTribeOfWashington: Coding = {
  code: '114',
  display: "Jamestown S'Klallam Tribe of Washington",
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_JamulIndianVillageOfCalifornia: Coding = {
  code: '115',
  display: 'Jamul Indian Village of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_JenaBandOfChoctawIndiansLouisiana: Coding = {
  code: '116',
  display: 'Jena Band of Choctaw Indians, Louisiana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_JicarillaApacheTribeOfTheJicarillaApacheInd: Coding = {
  code: '117',
  display: 'Jicarilla Apache Tribe of the Jicarilla Apache Ind',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KaibabBandOfPaiuteIndiansOfTheKaibabIndian: Coding = {
  code: '118',
  display: 'Kaibab Band of Paiute Indians of the Kaibab Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KalispelIndianCommunityOfTheKalispelReservat: Coding = {
  code: '119',
  display: 'Kalispel Indian Community of the Kalispel Reservat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BadRiverBandOfTheLakeSuperiorTribeOfChipp: Coding = {
  code: '12',
  display: 'Bad River Band of the Lake Superior Tribe of Chipp',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KarukTribeOfCalifornia: Coding = {
  code: '120',
  display: 'Karuk Tribe of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KashiaBandOfPomoIndiansOfTheStewartsPoint: Coding = {
  code: '121',
  display: 'Kashia Band of Pomo Indians of the Stewarts Point',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KawNationOklahoma: Coding = {
  code: '122',
  display: 'Kaw Nation, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KeweenawBayIndianCommunityOfLQuoteAnseAndOntona: Coding = {
  code: '123',
  display: "Keweenaw Bay Indian Community of L'Anse and Ontona",
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KialegeeTribalTownOklahoma: Coding = {
  code: '124',
  display: 'Kialegee Tribal Town, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KickapooTribeOfIndiansOfTheKickapooReservat: Coding = {
  code: '125',
  display: 'Kickapoo Tribe of Indians of the Kickapoo Reservat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KickapooTribeOfOklahoma: Coding = {
  code: '126',
  display: 'Kickapoo Tribe of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KickapooTraditionalTribeOfTexas: Coding = {
  code: '127',
  display: 'Kickapoo Traditional Tribe of Texas',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KiowaIndianTribeOfOklahoma: Coding = {
  code: '128',
  display: 'Kiowa Indian Tribe of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KlamathIndianTribeOfOregon: Coding = {
  code: '129',
  display: 'Klamath Indian Tribe of Oregon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BayMillsIndianCommunityOfTheSaultSteMarie: Coding = {
  code: '13',
  display: 'Bay Mills Indian Community of the Sault Ste. Marie',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KootenaiTribeOfIdaho: Coding = {
  code: '130',
  display: 'Kootenai Tribe of Idaho',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LaJollaBandOfLuisenoMissionIndiansOfTheLa: Coding = {
  code: '131',
  display: 'La Jolla Band of Luiseno Mission Indians of the La',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LaPostaBandOfDieguenoMissionIndiansOfTheL: Coding = {
  code: '132',
  display: 'La Posta Band of Diegueno Mission Indians of the L',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LacCourteOreillesBandOfLakeSuperiorChippewa: Coding = {
  code: '133',
  display: 'Lac Courte Oreilles Band of Lake Superior Chippewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LacDuFlambeauBandOfLakeSuperiorChippewaInd: Coding = {
  code: '134',
  display: 'Lac du Flambeau Band of Lake Superior Chippewa Ind',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LacVieuxDesertBandOfLakeSuperiorChippewaIn: Coding = {
  code: '135',
  display: 'Lac Vieux Desert Band of Lake Superior Chippewa In',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LasVegasTribeOfPaiuteIndiansOfTheLasVegas: Coding = {
  code: '136',
  display: 'Las Vegas Tribe of Paiute Indians of the Las Vegas',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LittleRiverBandOfOttawaIndiansOfMichigan: Coding = {
  code: '137',
  display: 'Little River Band of Ottawa Indians of Michigan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LittleTraverseBayBandsOfOdawaIndiansOfMich: Coding = {
  code: '138',
  display: 'Little Traverse Bay Bands of Odawa Indians of Mich',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LowerLakeRancheriaCalifornia: Coding = {
  code: '139',
  display: 'Lower Lake Rancheria, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BearRiverBandOfTheRohnervilleRancheriaCali: Coding = {
  code: '14',
  display: 'Bear River Band of the Rohnerville Rancheria, Cali',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LosCoyotesBandOfCahuillaMissionIndiansOfTh: Coding = {
  code: '140',
  display: 'Los Coyotes Band of Cahuilla Mission Indians of th',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LovelockPaiuteTribeOfTheLovelockIndianColon: Coding = {
  code: '141',
  display: 'Lovelock Paiute Tribe of the Lovelock Indian Colon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LowerBruleSiouxTribeOfTheLowerBruleReserva: Coding = {
  code: '142',
  display: 'Lower Brule Sioux Tribe of the Lower Brule Reserva',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LowerElwhaTribalCommunityOfTheLowerElwhaRe: Coding = {
  code: '143',
  display: 'Lower Elwha Tribal Community of the Lower Elwha Re',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LowerSiouxIndianCommunityOfMinnesotaMdewakan: Coding = {
  code: '144',
  display: 'Lower Sioux Indian Community of Minnesota Mdewakan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LummiTribeOfTheLummiReservationWashington: Coding = {
  code: '145',
  display: 'Lummi Tribe of the Lummi Reservation, Washington',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LyttonRancheriaOfCalifornia: Coding = {
  code: '146',
  display: 'Lytton Rancheria of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MakahIndianTribeOfTheMakahIndianReservation: Coding = {
  code: '147',
  display: 'Makah Indian Tribe of the Makah Indian Reservation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ManchesterBandOfPomoIndiansOfTheManchester: Coding = {
  code: '148',
  display: 'Manchester Band of Pomo Indians of the Manchester-',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ManzanitaBandOfDieguenoMissionIndiansOfThe: Coding = {
  code: '149',
  display: 'Manzanita Band of Diegueno Mission Indians of the',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BerryCreekRancheriaOfMaiduIndiansOfCaliforn: Coding = {
  code: '15',
  display: 'Berry Creek Rancheria of Maidu Indians of Californ',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MashantucketPequotTribeOfConnecticut: Coding = {
  code: '150',
  display: 'Mashantucket Pequot Tribe of Connecticut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MatchEBeNashSheWishBandOfPottawatomiIndia: Coding = {
  code: '151',
  display: 'Match-e-be-nash-she-wish Band of Pottawatomi India',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MechoopdaIndianTribeOfChicoRancheriaCalifor: Coding = {
  code: '152',
  display: 'Mechoopda Indian Tribe of Chico Rancheria, Califor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MenomineeIndianTribeOfWisconsin: Coding = {
  code: '153',
  display: 'Menominee Indian Tribe of Wisconsin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MesaGrandeBandOfDieguenoMissionIndiansOfTh: Coding = {
  code: '154',
  display: 'Mesa Grande Band of Diegueno Mission Indians of th',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MescaleroApacheTribeOfTheMescaleroReservatio: Coding = {
  code: '155',
  display: 'Mescalero Apache Tribe of the Mescalero Reservatio',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MiamiTribeOfOklahoma: Coding = {
  code: '156',
  display: 'Miami Tribe of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MiccosukeeTribeOfIndiansOfFlorida: Coding = {
  code: '157',
  display: 'Miccosukee Tribe of Indians of Florida',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MiddletownRancheriaOfPomoIndiansOfCalifornia: Coding = {
  code: '158',
  display: 'Middletown Rancheria of Pomo Indians of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MinnesotaChippewaTribeMinnesotaSixComponent: Coding = {
  code: '159',
  display: 'Minnesota Chippewa Tribe, Minnesota (Six component',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BigLagoonRancheriaCalifornia: Coding = {
  code: '16',
  display: 'Big Lagoon Rancheria, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BoisForteBandNettLakeFondDuLacBandGra: Coding = {
  code: '160',
  display: 'Bois Forte Band (Nett Lake); Fond du Lac Band; Gra',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MississippiBandOfChoctawIndiansMississippi: Coding = {
  code: '161',
  display: 'Mississippi Band of Choctaw Indians, Mississippi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MoapaBandOfPaiuteIndiansOfTheMoapaRiverIn: Coding = {
  code: '162',
  display: 'Moapa Band of Paiute Indians of the Moapa River In',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ModocTribeOfOklahoma: Coding = {
  code: '163',
  display: 'Modoc Tribe of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MoheganIndianTribeOfConnecticut: Coding = {
  code: '164',
  display: 'Mohegan Indian Tribe of Connecticut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MooretownRancheriaOfMaiduIndiansOfCalifornia: Coding = {
  code: '165',
  display: 'Mooretown Rancheria of Maidu Indians of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MorongoBandOfCahuillaMissionIndiansOfTheMo: Coding = {
  code: '166',
  display: 'Morongo Band of Cahuilla Mission Indians of the Mo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MuckleshootIndianTribeOfTheMuckleshootReserv: Coding = {
  code: '167',
  display: 'Muckleshoot Indian Tribe of the Muckleshoot Reserv',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MuscogeeCreekNationOklahoma: Coding = {
  code: '168',
  display: 'Muscogee (Creek) Nation, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NarragansettIndianTribeOfRhodeIsland: Coding = {
  code: '169',
  display: 'Narragansett Indian Tribe of Rhode Island',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BigPineBandOfOwensValleyPaiuteShoshoneIndi: Coding = {
  code: '17',
  display: 'Big Pine Band of Owens Valley Paiute Shoshone Indi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NavajoNationArizonaNewMexicoAndUtah: Coding = {
  code: '170',
  display: 'Navajo Nation, Arizona, New Mexico & Utah',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NezPerceTribeOfIdaho: Coding = {
  code: '171',
  display: 'Nez Perce Tribe of Idaho',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NisquallyIndianTribeOfTheNisquallyReservatio: Coding = {
  code: '172',
  display: 'Nisqually Indian Tribe of the Nisqually Reservatio',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NooksackIndianTribeOfWashington: Coding = {
  code: '173',
  display: 'Nooksack Indian Tribe of Washington',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NorthernCheyenneTribeOfTheNorthernCheyenneI: Coding = {
  code: '174',
  display: 'Northern Cheyenne Tribe of the Northern Cheyenne I',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NorthforkRancheriaOfMonoIndiansOfCalifornia: Coding = {
  code: '175',
  display: 'Northfork Rancheria of Mono Indians of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NorthwesternBandOfShoshoniNationOfUtahWash: Coding = {
  code: '176',
  display: 'Northwestern Band of Shoshoni Nation of Utah (Wash',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OglalaSiouxTribeOfThePineRidgeReservation: Coding = {
  code: '177',
  display: 'Oglala Sioux Tribe of the Pine Ridge Reservation,',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OmahaTribeOfNebraska: Coding = {
  code: '178',
  display: 'Omaha Tribe of Nebraska',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OneidaNationOfNewYork: Coding = {
  code: '179',
  display: 'Oneida Nation of New York',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BigSandyRancheriaOfMonoIndiansOfCalifornia: Coding = {
  code: '18',
  display: 'Big Sandy Rancheria of Mono Indians of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OneidaTribeOfWisconsin: Coding = {
  code: '180',
  display: 'Oneida Tribe of Wisconsin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OnondagaNationOfNewYork: Coding = {
  code: '181',
  display: 'Onondaga Nation of New York',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OsageTribeOklahoma: Coding = {
  code: '182',
  display: 'Osage Tribe, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OttawaTribeOfOklahoma: Coding = {
  code: '183',
  display: 'Ottawa Tribe of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OtoeMissouriaTribeOfIndiansOklahoma: Coding = {
  code: '184',
  display: 'Otoe-Missouria Tribe of Indians, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PaiuteIndianTribeOfUtah: Coding = {
  code: '185',
  display: 'Paiute Indian Tribe of Utah',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PaiuteShoshoneIndiansOfTheBishopCommunityOf: Coding = {
  code: '186',
  display: 'Paiute-Shoshone Indians of the Bishop Community of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PaiuteShoshoneTribeOfTheFallonReservationAn: Coding = {
  code: '187',
  display: 'Paiute-Shoshone Tribe of the Fallon Reservation an',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PaiuteShoshoneIndiansOfTheLonePineCommunity: Coding = {
  code: '188',
  display: 'Paiute-Shoshone Indians of the Lone Pine Community',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PalaBandOfLuisenoMissionIndiansOfThePalaR: Coding = {
  code: '189',
  display: 'Pala Band of Luiseno Mission Indians of the Pala R',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BigValleyBandOfPomoIndiansOfTheBigValley: Coding = {
  code: '19',
  display: 'Big Valley Band of Pomo Indians of the Big Valley',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PascuaYaquiTribeOfArizona: Coding = {
  code: '190',
  display: 'Pascua Yaqui Tribe of Arizona',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PaskentaBandOfNomlakiIndiansOfCalifornia: Coding = {
  code: '191',
  display: 'Paskenta Band of Nomlaki Indians of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PassamaquoddyTribeOfMaine: Coding = {
  code: '192',
  display: 'Passamaquoddy Tribe of Maine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PaumaBandOfLuisenoMissionIndiansOfThePauma: Coding = {
  code: '193',
  display: 'Pauma Band of Luiseno Mission Indians of the Pauma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PawneeNationOfOklahoma: Coding = {
  code: '194',
  display: 'Pawnee Nation of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PechangaBandOfLuisenoMissionIndiansOfThePe: Coding = {
  code: '195',
  display: 'Pechanga Band of Luiseno Mission Indians of the Pe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PenobscotTribeOfMaine: Coding = {
  code: '196',
  display: 'Penobscot Tribe of Maine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PeoriaTribeOfIndiansOfOklahoma: Coding = {
  code: '197',
  display: 'Peoria Tribe of Indians of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PicayuneRancheriaOfChukchansiIndiansOfCalifo: Coding = {
  code: '198',
  display: 'Picayune Rancheria of Chukchansi Indians of Califo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PinolevilleRancheriaOfPomoIndiansOfCaliforni: Coding = {
  code: '199',
  display: 'Pinoleville Rancheria of Pomo Indians of Californi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AguaCalienteBandOfCahuillaIndiansOfTheAgua: Coding = {
  code: '2',
  display: 'Agua Caliente Band of Cahuilla Indians of the Agua',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BlackfeetTribeOfTheBlackfeetIndianReservatio: Coding = {
  code: '20',
  display: 'Blackfeet Tribe of the Blackfeet Indian Reservatio',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PitRiverTribeCaliforniaIncludesBigBendLo: Coding = {
  code: '200',
  display: 'Pit River Tribe, California (includes Big Bend, Lo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PoarchBandOfCreekIndiansOfAlabama: Coding = {
  code: '201',
  display: 'Poarch Band of Creek Indians of Alabama',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PokagonBandOfPotawatomiIndiansOfMichigan: Coding = {
  code: '202',
  display: 'Pokagon Band of Potawatomi Indians of Michigan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PoncaTribeOfIndiansOfOklahoma: Coding = {
  code: '203',
  display: 'Ponca Tribe of Indians of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PoncaTribeOfNebraska: Coding = {
  code: '204',
  display: 'Ponca Tribe of Nebraska',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PortGambleIndianCommunityOfThePortGambleRe: Coding = {
  code: '205',
  display: 'Port Gamble Indian Community of the Port Gamble Re',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PotterValleyRancheriaOfPomoIndiansOfCalifor: Coding = {
  code: '206',
  display: 'Potter Valley Rancheria of Pomo Indians of Califor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PrairieBandOfPotawatomiIndiansKansas: Coding = {
  code: '207',
  display: 'Prairie Band of Potawatomi Indians, Kansas',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PrairieIslandIndianCommunityOfMinnesotaMdewa: Coding = {
  code: '208',
  display: 'Prairie Island Indian Community of Minnesota Mdewa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfAcomaNewMexico: Coding = {
  code: '209',
  display: 'Pueblo of Acoma, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BlueLakeRancheriaCalifornia: Coding = {
  code: '21',
  display: 'Blue Lake Rancheria, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfCochitiNewMexico: Coding = {
  code: '210',
  display: 'Pueblo of Cochiti, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfJemezNewMexico: Coding = {
  code: '211',
  display: 'Pueblo of Jemez, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfIsletaNewMexico: Coding = {
  code: '212',
  display: 'Pueblo of Isleta, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfLagunaNewMexico: Coding = {
  code: '213',
  display: 'Pueblo of Laguna, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfNambeNewMexico: Coding = {
  code: '214',
  display: 'Pueblo of Nambe, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfPicurisNewMexico: Coding = {
  code: '215',
  display: 'Pueblo of Picuris, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfPojoaqueNewMexico: Coding = {
  code: '216',
  display: 'Pueblo of Pojoaque, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfSanFelipeNewMexico: Coding = {
  code: '217',
  display: 'Pueblo of San Felipe, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfSanJuanNewMexico: Coding = {
  code: '218',
  display: 'Pueblo of San Juan, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfSanIldefonsoNewMexico: Coding = {
  code: '219',
  display: 'Pueblo of San Ildefonso, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BridgeportPaiuteIndianColonyOfCalifornia: Coding = {
  code: '22',
  display: 'Bridgeport Paiute Indian Colony of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfSandiaNewMexico: Coding = {
  code: '220',
  display: 'Pueblo of Sandia, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfSantaAnaNewMexico: Coding = {
  code: '221',
  display: 'Pueblo of Santa Ana, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfSantaClaraNewMexico: Coding = {
  code: '222',
  display: 'Pueblo of Santa Clara, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfSantoDomingoNewMexico: Coding = {
  code: '223',
  display: 'Pueblo of Santo Domingo, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfTaosNewMexico: Coding = {
  code: '224',
  display: 'Pueblo of Taos, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfTesuqueNewMexico: Coding = {
  code: '225',
  display: 'Pueblo of Tesuque, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuebloOfZiaNewMexico: Coding = {
  code: '226',
  display: 'Pueblo of Zia, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PuyallupTribeOfThePuyallupReservationWashin: Coding = {
  code: '227',
  display: 'Puyallup Tribe of the Puyallup Reservation, Washin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PyramidLakePaiuteTribeOfThePyramidLakeRese: Coding = {
  code: '228',
  display: 'Pyramid Lake Paiute Tribe of the Pyramid Lake Rese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_QuapawTribeOfIndiansOklahoma: Coding = {
  code: '229',
  display: 'Quapaw Tribe of Indians, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BuenaVistaRancheriaOfMeWukIndiansOfCalifor: Coding = {
  code: '23',
  display: 'Buena Vista Rancheria of Me-Wuk Indians of Califor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_QuartzValleyIndianCommunityOfTheQuartzValle: Coding = {
  code: '230',
  display: 'Quartz Valley Indian Community of the Quartz Valle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_QuechanTribeOfTheFortYumaIndianReservation: Coding = {
  code: '231',
  display: 'Quechan Tribe of the Fort Yuma Indian Reservation,',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_QuileuteTribeOfTheQuileuteReservationWashin: Coding = {
  code: '232',
  display: 'Quileute Tribe of the Quileute Reservation, Washin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_QuinaultTribeOfTheQuinaultReservationWashin: Coding = {
  code: '233',
  display: 'Quinault Tribe of the Quinault Reservation, Washin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_RamonaBandOrVillageOfCahuillaMissionIndians: Coding = {
  code: '234',
  display: 'Ramona Band or Village of Cahuilla Mission Indians',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_RedCliffBandOfLakeSuperiorChippewaIndiansO: Coding = {
  code: '235',
  display: 'Red Cliff Band of Lake Superior Chippewa Indians o',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_RedLakeBandOfChippewaIndiansOfTheRedLake: Coding = {
  code: '236',
  display: 'Red Lake Band of Chippewa Indians of the Red Lake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ReddingRancheriaCalifornia: Coding = {
  code: '237',
  display: 'Redding Rancheria, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_RedwoodValleyRancheriaOfPomoIndiansOfCalifo: Coding = {
  code: '238',
  display: 'Redwood Valley Rancheria of Pomo Indians of Califo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_RenoSparksIndianColonyNevada: Coding = {
  code: '239',
  display: 'Reno-Sparks Indian Colony, Nevada',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BurnsPaiuteTribeOfTheBurnsPaiuteIndianColo: Coding = {
  code: '24',
  display: 'Burns Paiute Tribe of the Burns Paiute Indian Colo',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ResighiniRancheriaCaliforniaFormerlyKnownAs: Coding = {
  code: '240',
  display: 'Resighini Rancheria, California (formerly known as',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_RinconBandOfLuisenoMissionIndiansOfTheRinc: Coding = {
  code: '241',
  display: 'Rincon Band of Luiseno Mission Indians of the Rinc',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_RobinsonRancheriaOfPomoIndiansOfCalifornia: Coding = {
  code: '242',
  display: 'Robinson Rancheria of Pomo Indians of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_RosebudSiouxTribeOfTheRosebudIndianReservat: Coding = {
  code: '243',
  display: 'Rosebud Sioux Tribe of the Rosebud Indian Reservat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_RoundValleyIndianTribesOfTheRoundValleyRes: Coding = {
  code: '244',
  display: 'Round Valley Indian Tribes of the Round Valley Res',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_RumseyIndianRancheriaOfWintunIndiansOfCalif: Coding = {
  code: '245',
  display: 'Rumsey Indian Rancheria of Wintun Indians of Calif',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SacAndFoxTribeOfTheMississippiInIowa: Coding = {
  code: '246',
  display: 'Sac and Fox Tribe of the Mississippi in Iowa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SacAndFoxNationOfMissouriInKansasAndNebra: Coding = {
  code: '247',
  display: 'Sac and Fox Nation of Missouri in Kansas and Nebra',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SacAndFoxNationOklahoma: Coding = {
  code: '248',
  display: 'Sac and Fox Nation, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SaginawChippewaIndianTribeOfMichiganIsabell: Coding = {
  code: '249',
  display: 'Saginaw Chippewa Indian Tribe of Michigan, Isabell',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CabazonBandOfCahuillaMissionIndiansOfTheCa: Coding = {
  code: '25',
  display: 'Cabazon Band of Cahuilla Mission Indians of the Ca',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SaltRiverPimaMaricopaIndianCommunityOfTheS: Coding = {
  code: '250',
  display: 'Salt River Pima-Maricopa Indian Community of the S',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SamishIndianTribeWashington: Coding = {
  code: '251',
  display: 'Samish Indian Tribe, Washington',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SanCarlosApacheTribeOfTheSanCarlosReservat: Coding = {
  code: '252',
  display: 'San Carlos Apache Tribe of the San Carlos Reservat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SanJuanSouthernPaiuteTribeOfArizona: Coding = {
  code: '253',
  display: 'San Juan Southern Paiute Tribe of Arizona',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SanManualBandOfSerranoMissionIndiansOfThe: Coding = {
  code: '254',
  display: 'San Manual Band of Serrano Mission Indians of the',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SanPasqualBandOfDieguenoMissionIndiansOfCa: Coding = {
  code: '255',
  display: 'San Pasqual Band of Diegueno Mission Indians of Ca',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SantaRosaIndianCommunityOfTheSantaRosaRanc: Coding = {
  code: '256',
  display: 'Santa Rosa Indian Community of the Santa Rosa Ranc',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SantaRosaBandOfCahuillaMissionIndiansOfThe: Coding = {
  code: '257',
  display: 'Santa Rosa Band of Cahuilla Mission Indians of the',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SantaYnezBandOfChumashMissionIndiansOfThe: Coding = {
  code: '258',
  display: 'Santa Ynez Band of Chumash Mission Indians of the',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SantaYsabelBandOfDieguenoMissionIndiansOfT: Coding = {
  code: '259',
  display: 'Santa Ysabel Band of Diegueno Mission Indians of t',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CachilDeHeBandOfWintunIndiansOfTheColusaI: Coding = {
  code: '26',
  display: 'Cachil DeHe Band of Wintun Indians of the Colusa I',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SanteeSiouxTribeOfTheSanteeReservationOfNe: Coding = {
  code: '260',
  display: 'Santee Sioux Tribe of the Santee Reservation of Ne',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SaukSuiattleIndianTribeOfWashington: Coding = {
  code: '261',
  display: 'Sauk-Suiattle Indian Tribe of Washington',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SaultSteMarieTribeOfChippewaIndiansOfMich: Coding = {
  code: '262',
  display: 'Sault Ste. Marie Tribe of Chippewa Indians of Mich',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ScottsValleyBandOfPomoIndiansOfCalifornia: Coding = {
  code: '263',
  display: 'Scotts Valley Band of Pomo Indians of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SeminoleNationOfOklahoma: Coding = {
  code: '264',
  display: 'Seminole Nation of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SeminoleTribeOfFloridaDaniaBigCypressBri: Coding = {
  code: '265',
  display: 'Seminole Tribe of Florida, Dania, Big Cypress, Bri',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SenecaNationOfNewYork: Coding = {
  code: '266',
  display: 'Seneca Nation of New York',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SenecaCayugaTribeOfOklahoma: Coding = {
  code: '267',
  display: 'Seneca-Cayuga Tribe of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ShakopeeMdewakantonSiouxCommunityOfMinnesota: Coding = {
  code: '268',
  display: 'Shakopee Mdewakanton Sioux Community of Minnesota',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ShawneeTribeOklahoma: Coding = {
  code: '269',
  display: 'Shawnee Tribe, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CaddoIndianTribeOfOklahoma: Coding = {
  code: '27',
  display: 'Caddo Indian Tribe of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SherwoodValleyRancheriaOfPomoIndiansOfCalif: Coding = {
  code: '270',
  display: 'Sherwood Valley Rancheria of Pomo Indians of Calif',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ShingleSpringsBandOfMiwokIndiansShingleSpr: Coding = {
  code: '271',
  display: 'Shingle Springs Band of Miwok Indians, Shingle Spr',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ShoalwaterBayTribeOfTheShoalwaterBayIndian: Coding = {
  code: '272',
  display: 'Shoalwater Bay Tribe of the Shoalwater Bay Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ShoshoneTribeOfTheWindRiverReservationWyom: Coding = {
  code: '273',
  display: 'Shoshone Tribe of the Wind River Reservation, Wyom',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ShoshoneBannockTribesOfTheFortHallReservati: Coding = {
  code: '274',
  display: 'Shoshone-Bannock Tribes of the Fort Hall Reservati',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ShoshonePaiuteTribesOfTheDuckValleyReservat: Coding = {
  code: '275',
  display: 'Shoshone-Paiute Tribes of the Duck Valley Reservat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SissetonWahpetonSiouxTribeOfTheLakeTraverse: Coding = {
  code: '276',
  display: 'Sisseton-Wahpeton Sioux Tribe of the Lake Traverse',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SkokomishIndianTribeOfTheSkokomishReservatio: Coding = {
  code: '277',
  display: 'Skokomish Indian Tribe of the Skokomish Reservatio',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SkullValleyBandOfGoshuteIndiansOfUtah: Coding = {
  code: '278',
  display: 'Skull Valley Band of Goshute Indians of Utah',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SmithRiverRancheriaCalifornia: Coding = {
  code: '279',
  display: 'Smith River Rancheria, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CahuillaBandOfMissionIndiansOfTheCahuillaR: Coding = {
  code: '28',
  display: 'Cahuilla Band of Mission Indians of the Cahuilla R',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SnoqualmieTribeWashington: Coding = {
  code: '280',
  display: 'Snoqualmie Tribe, Washington',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SobobaBandOfLuisenoIndiansCaliforniaFormer: Coding = {
  code: '281',
  display: 'Soboba Band of Luiseno Indians, California (former',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SokaogonChippewaCommunityOfTheMoleLakeBand: Coding = {
  code: '282',
  display: 'Sokaogon Chippewa Community of the Mole Lake Band',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SouthernUteIndianTribeOfTheSouthernUteRese: Coding = {
  code: '283',
  display: 'Southern Ute Indian Tribe of the Southern Ute Rese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SpiritLakeTribeNorthDakotaFormerlyKnownAs: Coding = {
  code: '284',
  display: 'Spirit Lake Tribe, North Dakota (formerly known as',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SpokaneTribeOfTheSpokaneReservationWashingt: Coding = {
  code: '285',
  display: 'Spokane Tribe of the Spokane Reservation, Washingt',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SquaxinIslandTribeOfTheSquaxinIslandReserva: Coding = {
  code: '286',
  display: 'Squaxin Island Tribe of the Squaxin Island Reserva',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_StCroixChippewaIndiansOfWisconsinStCroix: Coding = {
  code: '287',
  display: 'St. Croix Chippewa Indians of Wisconsin, St. Croix',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_StRegisBandOfMohawkIndiansOfNewYork: Coding = {
  code: '288',
  display: 'St. Regis Band of Mohawk Indians of New York',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_StandingRockSiouxTribeOfNorthAndSouthDakota: Coding = {
  code: '289',
  display: 'Standing Rock Sioux Tribe of North & South Dakota',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CahtoIndianTribeOfTheLaytonvilleRancheriaC: Coding = {
  code: '29',
  display: 'Cahto Indian Tribe of the Laytonville Rancheria, C',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_StockbridgeMunseeCommunityOfMohicanIndiansOf: Coding = {
  code: '290',
  display: 'Stockbridge-Munsee Community of Mohican Indians of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_StillaguamishTribeOfWashington: Coding = {
  code: '291',
  display: 'Stillaguamish Tribe of Washington',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SummitLakePaiuteTribeOfNevada: Coding = {
  code: '292',
  display: 'Summit Lake Paiute Tribe of Nevada',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SuquamishIndianTribeOfThePortMadisonReserva: Coding = {
  code: '293',
  display: 'Suquamish Indian Tribe of the Port Madison Reserva',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SusanvilleIndianRancheriaCalifornia: Coding = {
  code: '294',
  display: 'Susanville Indian Rancheria, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SwinomishIndiansOfTheSwinomishReservationWa: Coding = {
  code: '295',
  display: 'Swinomish Indians of the Swinomish Reservation, Wa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SycuanBandOfDieguenoMissionIndiansOfCalifor: Coding = {
  code: '296',
  display: 'Sycuan Band of Diegueno Mission Indians of Califor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TableBluffReservationWiyotTribeCalifornia: Coding = {
  code: '297',
  display: 'Table Bluff Reservation - Wiyot Tribe, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TableMountainRancheriaOfCalifornia: Coding = {
  code: '298',
  display: 'Table Mountain Rancheria of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TeMoakTribeOfWesternShoshoneIndiansOfNevad: Coding = {
  code: '299',
  display: 'Te-Moak Tribe of Western Shoshone Indians of Nevad',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AkChinIndianCommunityOfTheMaricopaAkChin: Coding = {
  code: '3',
  display: 'Ak Chin Indian Community of the Maricopa (Ak Chin)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CaliforniaValleyMiwokTribeCaliforniaFormerl: Coding = {
  code: '30',
  display: 'California Valley Miwok Tribe, California (formerl',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ThlopthloccoTribalTownOklahoma: Coding = {
  code: '300',
  display: 'Thlopthlocco Tribal Town, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ThreeAffiliatedTribesOfTheFortBertholdReser: Coding = {
  code: '301',
  display: 'Three Affiliated Tribes of the Fort Berthold Reser',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TohonoOQuoteOdhamNationOfArizona: Coding = {
  code: '302',
  display: "Tohono O'odham Nation of Arizona",
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TonawandaBandOfSenecaIndiansOfNewYork: Coding = {
  code: '303',
  display: 'Tonawanda Band of Seneca Indians of New York',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TonkawaTribeOfIndiansOfOklahoma: Coding = {
  code: '304',
  display: 'Tonkawa Tribe of Indians of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TontoApacheTribeOfArizona: Coding = {
  code: '305',
  display: 'Tonto Apache Tribe of Arizona',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TorresMartinezBandOfCahuillaMissionIndiansO: Coding = {
  code: '306',
  display: 'Torres-Martinez Band of Cahuilla Mission Indians o',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TuleRiverIndianTribeOfTheTuleRiverReservat: Coding = {
  code: '307',
  display: 'Tule River Indian Tribe of the Tule River Reservat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TulalipTribesOfTheTulalipReservationWashing: Coding = {
  code: '308',
  display: 'Tulalip Tribes of the Tulalip Reservation, Washing',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TunicaBiloxiIndianTribeOfLouisiana: Coding = {
  code: '309',
  display: 'Tunica-Biloxi Indian Tribe of Louisiana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CampoBandOfDieguenoMissionIndiansOfTheCamp: Coding = {
  code: '31',
  display: 'Campo Band of Diegueno Mission Indians of the Camp',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TuolumneBandOfMeWukIndiansOfTheTuolumneRa: Coding = {
  code: '310',
  display: 'Tuolumne Band of Me-Wuk Indians of the Tuolumne Ra',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TurtleMountainBandOfChippewaIndiansOfNorth: Coding = {
  code: '311',
  display: 'Turtle Mountain Band of Chippewa Indians of North',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TuscaroraNationOfNewYork: Coding = {
  code: '312',
  display: 'Tuscarora Nation of New York',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TwentyNinePalmsBandOfMissionIndiansOfCalif: Coding = {
  code: '313',
  display: 'Twenty-Nine Palms Band of Mission Indians of Calif',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_UnitedAuburnIndianCommunityOfTheAuburnRanch: Coding = {
  code: '314',
  display: 'United Auburn Indian Community of the Auburn Ranch',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_UnitedKeetoowahBandOfCherokeeIndiansOfOklah: Coding = {
  code: '315',
  display: 'United Keetoowah Band of Cherokee Indians of Oklah',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_UpperLakeBandOfPomoIndiansOfUpperLakeRanc: Coding = {
  code: '316',
  display: 'Upper Lake Band of Pomo Indians of Upper Lake Ranc',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_UpperSiouxIndianCommunityOfTheUpperSiouxRe: Coding = {
  code: '317',
  display: 'Upper Sioux Indian Community of the Upper Sioux Re',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_UpperSkagitIndianTribeOfWashington: Coding = {
  code: '318',
  display: 'Upper Skagit Indian Tribe of Washington',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_UteIndianTribeOfTheUintahAndOurayReservation: Coding = {
  code: '319',
  display: 'Ute Indian Tribe of the Uintah & Ouray Reservation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CapitanGrandeBandOfDieguenoMissionIndiansOf: Coding = {
  code: '32',
  display: 'Capitan Grande Band of Diegueno Mission Indians of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_UteMountainTribeOfTheUteMountainReservation: Coding = {
  code: '320',
  display: 'Ute Mountain Tribe of the Ute Mountain Reservation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_UtuUtuGwaituPaiuteTribeOfTheBentonPaiuteR: Coding = {
  code: '321',
  display: 'Utu Utu Gwaitu Paiute Tribe of the Benton Paiute R',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_WalkerRiverPaiuteTribeOfTheWalkerRiverRese: Coding = {
  code: '322',
  display: 'Walker River Paiute Tribe of the Walker River Rese',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_WampanoagTribeOfGayHeadAquinnahOfMassachu: Coding = {
  code: '323',
  display: 'Wampanoag Tribe of Gay Head (Aquinnah) of Massachu',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_WashoeTribeOfNevadaAndCaliforniaCarsonColony: Coding = {
  code: '324',
  display: 'Washoe Tribe of Nevada & California (Carson Colony',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_WhiteMountainApacheTribeOfTheFortApacheRes: Coding = {
  code: '325',
  display: 'White Mountain Apache Tribe of the Fort Apache Res',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_WichitaAndAffiliatedTribesWichitaKeechiWa: Coding = {
  code: '326',
  display: 'Wichita and Affiliated Tribes (Wichita, Keechi, Wa',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_WinnebagoTribeOfNebraska: Coding = {
  code: '327',
  display: 'Winnebago Tribe of Nebraska',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_WinnemuccaIndianColonyOfNevada: Coding = {
  code: '328',
  display: 'Winnemucca Indian Colony of Nevada',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_WyandotteTribeOfOklahoma: Coding = {
  code: '329',
  display: 'Wyandotte Tribe of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BaronaGroupOfCapitanGrandeBandOfMissionInd: Coding = {
  code: '33',
  display: 'Barona Group of Capitan Grande Band of Mission Ind',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_YanktonSiouxTribeOfSouthDakota: Coding = {
  code: '330',
  display: 'Yankton Sioux Tribe of South Dakota',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_YavapaiApacheNationOfTheCampVerdeIndianRes: Coding = {
  code: '331',
  display: 'Yavapai-Apache Nation of the Camp Verde Indian Res',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_YavapaiPrescottTribeOfTheYavapaiReservation: Coding = {
  code: '332',
  display: 'Yavapai-Prescott Tribe of the Yavapai Reservation,',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_YeringtonPaiuteTribeOfTheYeringtonColonyAndC: Coding = {
  code: '333',
  display: 'Yerington Paiute Tribe of the Yerington Colony & C',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_YombaShoshoneTribeOfTheYombaReservationNev: Coding = {
  code: '334',
  display: 'Yomba Shoshone Tribe of the Yomba Reservation, Nev',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_YsletaDelSurPuebloOfTexas: Coding = {
  code: '335',
  display: 'Ysleta Del Sur Pueblo of Texas',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_YurokTribeOfTheYurokReservationCalifornia: Coding = {
  code: '336',
  display: 'Yurok Tribe of the Yurok Reservation, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ZuniTribeOfTheZuniReservationNewMexico: Coding = {
  code: '337',
  display: 'Zuni Tribe of the Zuni Reservation, New Mexico',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfAfognak: Coding = {
  code: '338',
  display: 'Village of Afognak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AgdaaguxTribeOfKingCove: Coding = {
  code: '339',
  display: 'Agdaagux Tribe of King Cove',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ViejasBaronLongGroupOfCapitanGrandeBandO: Coding = {
  code: '34',
  display: 'Viejas (Baron Long) Group of Capitan Grande Band o',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfAkhiok: Coding = {
  code: '340',
  display: 'Native Village of Akhiok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AkiachakNativeCommunity: Coding = {
  code: '341',
  display: 'Akiachak Native Community',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AkiakNativeCommunity: Coding = {
  code: '342',
  display: 'Akiak Native Community',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfAkutan: Coding = {
  code: '343',
  display: 'Native Village of Akutan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfAlakanuk: Coding = {
  code: '344',
  display: 'Village of Alakanuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AlatnaVillage: Coding = {
  code: '345',
  display: 'Alatna Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfAleknagik: Coding = {
  code: '346',
  display: 'Native Village of Aleknagik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AlgaaciqNativeVillageStMaryQuoteS: Coding = {
  code: '347',
  display: "Algaaciq Native Village (St. Mary's)",
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AllakaketVillage: Coding = {
  code: '348',
  display: 'Allakaket Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfAmbler: Coding = {
  code: '349',
  display: 'Native Village of Ambler',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CatawbaIndianNationAkaCatawbaTribeOfSouth: Coding = {
  code: '35',
  display: 'Catawba Indian Nation (aka Catawba Tribe of South',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfAnaktuvukPass: Coding = {
  code: '350',
  display: 'Village of Anaktuvuk Pass',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_YupiitOfAndreafski: Coding = {
  code: '351',
  display: 'Yupiit of Andreafski',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AngoonCommunityAssociation: Coding = {
  code: '352',
  display: 'Angoon Community Association',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfAniak: Coding = {
  code: '353',
  display: 'Village of Aniak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AnvikVillage: Coding = {
  code: '354',
  display: 'Anvik Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ArcticVillageSeeNativeVillageOfVenetieTrib: Coding = {
  code: '355',
  display: 'Arctic Village (See Native Village of Venetie Trib',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AsaCarsarmiutTribeFormerlyNativeVillageOfM: Coding = {
  code: '356',
  display: 'Asa carsarmiut Tribe (formerly Native Village of M',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfAtka: Coding = {
  code: '357',
  display: 'Native Village of Atka',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfAtmautluak: Coding = {
  code: '358',
  display: 'Village of Atmautluak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AtqasukVillageAtkasook: Coding = {
  code: '359',
  display: 'Atqasuk Village (Atkasook)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CayugaNationOfNewYork: Coding = {
  code: '36',
  display: 'Cayuga Nation of New York',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfBarrowInupiatTraditionalGover: Coding = {
  code: '360',
  display: 'Native Village of Barrow Inupiat Traditional Gover',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BeaverVillage: Coding = {
  code: '361',
  display: 'Beaver Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfBelkofski: Coding = {
  code: '362',
  display: 'Native Village of Belkofski',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfBillMooreQuoteSSlough: Coding = {
  code: '363',
  display: "Village of Bill Moore's Slough",
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_BirchCreekTribe: Coding = {
  code: '364',
  display: 'Birch Creek Tribe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfBrevigMission: Coding = {
  code: '365',
  display: 'Native Village of Brevig Mission',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfBuckland: Coding = {
  code: '366',
  display: 'Native Village of Buckland',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfCantwell: Coding = {
  code: '367',
  display: 'Native Village of Cantwell',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfChanegaAkaChenega: Coding = {
  code: '368',
  display: 'Native Village of Chanega (aka Chenega)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChalkyitsikVillage: Coding = {
  code: '369',
  display: 'Chalkyitsik Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CedarvilleRancheriaCalifornia: Coding = {
  code: '37',
  display: 'Cedarville Rancheria, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfChefornak: Coding = {
  code: '370',
  display: 'Village of Chefornak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChevakNativeVillage: Coding = {
  code: '371',
  display: 'Chevak Native Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChickaloonNativeVillage: Coding = {
  code: '372',
  display: 'Chickaloon Native Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfChignik: Coding = {
  code: '373',
  display: 'Native Village of Chignik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfChignikLagoon: Coding = {
  code: '374',
  display: 'Native Village of Chignik Lagoon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChignikLakeVillage: Coding = {
  code: '375',
  display: 'Chignik Lake Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChilkatIndianVillageKlukwan: Coding = {
  code: '376',
  display: 'Chilkat Indian Village (Klukwan)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChilkootIndianAssociationHaines: Coding = {
  code: '377',
  display: 'Chilkoot Indian Association (Haines)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChinikEskimoCommunityGolovin: Coding = {
  code: '378',
  display: 'Chinik Eskimo Community (Golovin)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfChistochina: Coding = {
  code: '379',
  display: 'Native Village of Chistochina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChemehueviIndianTribeOfTheChemehueviReservat: Coding = {
  code: '38',
  display: 'Chemehuevi Indian Tribe of the Chemehuevi Reservat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfChitina: Coding = {
  code: '380',
  display: 'Native Village of Chitina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfChuathbalukRussianMissionKu: Coding = {
  code: '381',
  display: 'Native Village of Chuathbaluk (Russian Mission, Ku',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChuloonawickNativeVillage: Coding = {
  code: '382',
  display: 'Chuloonawick Native Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CircleNativeCommunity: Coding = {
  code: '383',
  display: 'Circle Native Community',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfClarkQuoteSPoint: Coding = {
  code: '384',
  display: "Village of Clark's Point",
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfCouncil: Coding = {
  code: '385',
  display: 'Native Village of Council',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CraigCommunityAssociation: Coding = {
  code: '386',
  display: 'Craig Community Association',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfCrookedCreek: Coding = {
  code: '387',
  display: 'Village of Crooked Creek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CuryungTribalCouncilFormerlyNativeVillageOf: Coding = {
  code: '388',
  display: 'Curyung Tribal Council (formerly Native Village of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfDeering: Coding = {
  code: '389',
  display: 'Native Village of Deering',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CherAeHeightsIndianCommunityOfTheTrinidadR: Coding = {
  code: '39',
  display: 'Cher-Ae Heights Indian Community of the Trinidad R',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfDiomedeAkaInalik: Coding = {
  code: '390',
  display: 'Native Village of Diomede (aka Inalik)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfDotLake: Coding = {
  code: '391',
  display: 'Village of Dot Lake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_DouglasIndianAssociation: Coding = {
  code: '392',
  display: 'Douglas Indian Association',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfEagle: Coding = {
  code: '393',
  display: 'Native Village of Eagle',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfEek: Coding = {
  code: '394',
  display: 'Native Village of Eek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_EgegikVillage: Coding = {
  code: '395',
  display: 'Egegik Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_EklutnaNativeVillage: Coding = {
  code: '396',
  display: 'Eklutna Native Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfEkuk: Coding = {
  code: '397',
  display: 'Native Village of Ekuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_EkwokVillage: Coding = {
  code: '398',
  display: 'Ekwok Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfElim: Coding = {
  code: '399',
  display: 'Native Village of Elim',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AlabamaCoushattaTribesOfTexas: Coding = {
  code: '4',
  display: 'Alabama-Coushatta Tribes of Texas',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CherokeeNationOklahoma: Coding = {
  code: '40',
  display: 'Cherokee Nation, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_EmmonakVillage: Coding = {
  code: '400',
  display: 'Emmonak Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_EvansvilleVillageAkaBettlesField: Coding = {
  code: '401',
  display: 'Evansville Village (aka Bettles Field)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfEyakCordova: Coding = {
  code: '402',
  display: 'Native Village of Eyak (Cordova)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfFalsePass: Coding = {
  code: '403',
  display: 'Native Village of False Pass',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfFortYukon: Coding = {
  code: '404',
  display: 'Native Village of Fort Yukon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfGakona: Coding = {
  code: '405',
  display: 'Native Village of Gakona',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_GalenaVillageAkaLoudenVillage: Coding = {
  code: '406',
  display: 'Galena Village (aka Louden Village)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfGambell: Coding = {
  code: '407',
  display: 'Native Village of Gambell',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfGeorgetown: Coding = {
  code: '408',
  display: 'Native Village of Georgetown',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfGoodnewsBay: Coding = {
  code: '409',
  display: 'Native Village of Goodnews Bay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CheyenneArapahoTribesOfOklahoma: Coding = {
  code: '41',
  display: 'Cheyenne-Arapaho Tribes of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OrganizedVillageOfGraylingAkaHolikachuk: Coding = {
  code: '410',
  display: 'Organized Village of Grayling (aka Holikachuk)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_GulkanaVillage: Coding = {
  code: '411',
  display: 'Gulkana Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfHamilton: Coding = {
  code: '412',
  display: 'Native Village of Hamilton',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HealyLakeVillage: Coding = {
  code: '413',
  display: 'Healy Lake Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HolyCrossVillage: Coding = {
  code: '414',
  display: 'Holy Cross Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HoonahIndianAssociation: Coding = {
  code: '415',
  display: 'Hoonah Indian Association',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfHooperBay: Coding = {
  code: '416',
  display: 'Native Village of Hooper Bay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HughesVillage: Coding = {
  code: '417',
  display: 'Hughes Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HusliaVillage: Coding = {
  code: '418',
  display: 'Huslia Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HydaburgCooperativeAssociation: Coding = {
  code: '419',
  display: 'Hydaburg Cooperative Association',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CheyenneRiverSiouxTribeOfTheCheyenneRiverR: Coding = {
  code: '42',
  display: 'Cheyenne River Sioux Tribe of the Cheyenne River R',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_IgiugigVillage: Coding = {
  code: '420',
  display: 'Igiugig Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfIliamna: Coding = {
  code: '421',
  display: 'Village of Iliamna',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_InupiatCommunityOfTheArcticSlope: Coding = {
  code: '422',
  display: 'Inupiat Community of the Arctic Slope',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_IqurmuitTraditionalCouncilFormerlyNativeVill: Coding = {
  code: '423',
  display: 'Iqurmuit Traditional Council (formerly Native Vill',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_IvanoffBayVillage: Coding = {
  code: '424',
  display: 'Ivanoff Bay Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KaguyakVillage: Coding = {
  code: '425',
  display: 'Kaguyak Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OrganizedVillageOfKake: Coding = {
  code: '426',
  display: 'Organized Village of Kake',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KaktovikVillageAkaBarterIsland: Coding = {
  code: '427',
  display: 'Kaktovik Village (aka Barter Island)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfKalskag: Coding = {
  code: '428',
  display: 'Village of Kalskag',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfKaltag: Coding = {
  code: '429',
  display: 'Village of Kaltag',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChickasawNationOklahoma: Coding = {
  code: '43',
  display: 'Chickasaw Nation, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfKanatak: Coding = {
  code: '430',
  display: 'Native Village of Kanatak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfKarluk: Coding = {
  code: '431',
  display: 'Native Village of Karluk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OrganizedVillageOfKasaan: Coding = {
  code: '432',
  display: 'Organized Village of Kasaan',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfKasigluk: Coding = {
  code: '433',
  display: 'Native Village of Kasigluk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KenaitzeIndianTribe: Coding = {
  code: '434',
  display: 'Kenaitze Indian Tribe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KetchikanIndianCorporation: Coding = {
  code: '435',
  display: 'Ketchikan Indian Corporation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfKiana: Coding = {
  code: '436',
  display: 'Native Village of Kiana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KingIslandNativeCommunity: Coding = {
  code: '437',
  display: 'King Island Native Community',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KingSalmonTribe: Coding = {
  code: '438',
  display: 'King Salmon Tribe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfKipnuk: Coding = {
  code: '439',
  display: 'Native Village of Kipnuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChickenRanchRancheriaOfMeWukIndiansOfCalif: Coding = {
  code: '44',
  display: 'Chicken Ranch Rancheria of Me-Wuk Indians of Calif',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfKivalina: Coding = {
  code: '440',
  display: 'Native Village of Kivalina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KlawockCooperativeAssociation: Coding = {
  code: '441',
  display: 'Klawock Cooperative Association',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfKlutiKaahAkaCopperCenter: Coding = {
  code: '442',
  display: 'Native Village of Kluti Kaah (aka Copper Center)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KnikTribe: Coding = {
  code: '443',
  display: 'Knik Tribe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfKobuk: Coding = {
  code: '444',
  display: 'Native Village of Kobuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KokhanokVillage: Coding = {
  code: '445',
  display: 'Kokhanok Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfKongiganak: Coding = {
  code: '446',
  display: 'Native Village of Kongiganak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfKotlik: Coding = {
  code: '447',
  display: 'Village of Kotlik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfKotzebue: Coding = {
  code: '448',
  display: 'Native Village of Kotzebue',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfKoyuk: Coding = {
  code: '449',
  display: 'Native Village of Koyuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChippewaCreeIndiansOfTheRockyBoyQuoteSReservati: Coding = {
  code: '45',
  display: "Chippewa-Cree Indians of the Rocky Boy's Reservati",
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_KoyukukNativeVillage: Coding = {
  code: '450',
  display: 'Koyukuk Native Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OrganizedVillageOfKwethluk: Coding = {
  code: '451',
  display: 'Organized Village of Kwethluk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfKwigillingok: Coding = {
  code: '452',
  display: 'Native Village of Kwigillingok',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfKwinhagakAkaQuinhagak: Coding = {
  code: '453',
  display: 'Native Village of Kwinhagak (aka Quinhagak)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfLarsenBay: Coding = {
  code: '454',
  display: 'Native Village of Larsen Bay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LevelockVillage: Coding = {
  code: '455',
  display: 'Levelock Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LesnoiVillageAkaWoodyIsland: Coding = {
  code: '456',
  display: 'Lesnoi Village (aka Woody Island)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_LimeVillage: Coding = {
  code: '457',
  display: 'Lime Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfLowerKalskag: Coding = {
  code: '458',
  display: 'Village of Lower Kalskag',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ManleyHotSpringsVillage: Coding = {
  code: '459',
  display: 'Manley Hot Springs Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChitimachaTribeOfLouisiana: Coding = {
  code: '46',
  display: 'Chitimacha Tribe of Louisiana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ManokotakVillage: Coding = {
  code: '460',
  display: 'Manokotak Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfMarshallAkaFortunaLedge: Coding = {
  code: '461',
  display: 'Native Village of Marshall (aka Fortuna Ledge)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfMaryQuoteSIgloo: Coding = {
  code: '462',
  display: "Native Village of Mary's Igloo",
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_McGrathNativeVillage: Coding = {
  code: '463',
  display: 'McGrath Native Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfMekoryuk: Coding = {
  code: '464',
  display: 'Native Village of Mekoryuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MentastaTraditionalCouncil: Coding = {
  code: '465',
  display: 'Mentasta Traditional Council',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_MetlakatlaIndianCommunityAnnetteIslandReserv: Coding = {
  code: '466',
  display: 'Metlakatla Indian Community, Annette Island Reserv',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfMinto: Coding = {
  code: '467',
  display: 'Native Village of Minto',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NaknekNativeVillage: Coding = {
  code: '468',
  display: 'Naknek Native Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfNanwalekAkaEnglishBay: Coding = {
  code: '469',
  display: 'Native Village of Nanwalek (aka English Bay)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ChoctawNationOfOklahoma: Coding = {
  code: '47',
  display: 'Choctaw Nation of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfNapaimute: Coding = {
  code: '470',
  display: 'Native Village of Napaimute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfNapakiak: Coding = {
  code: '471',
  display: 'Native Village of Napakiak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfNapaskiak: Coding = {
  code: '472',
  display: 'Native Village of Napaskiak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfNelsonLagoon: Coding = {
  code: '473',
  display: 'Native Village of Nelson Lagoon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NenanaNativeAssociation: Coding = {
  code: '474',
  display: 'Nenana Native Association',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NewKoliganekVillageCouncilFormerlyKoliganek: Coding = {
  code: '475',
  display: 'New Koliganek Village Council (formerly Koliganek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NewStuyahokVillage: Coding = {
  code: '476',
  display: 'New Stuyahok Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NewhalenVillage: Coding = {
  code: '477',
  display: 'Newhalen Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NewtokVillage: Coding = {
  code: '478',
  display: 'Newtok Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfNightmute: Coding = {
  code: '479',
  display: 'Native Village of Nightmute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CitizenPotawatomiNationOklahoma: Coding = {
  code: '48',
  display: 'Citizen Potawatomi Nation, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NikolaiVillage: Coding = {
  code: '480',
  display: 'Nikolai Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfNikolski: Coding = {
  code: '481',
  display: 'Native Village of Nikolski',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NinilchikVillage: Coding = {
  code: '482',
  display: 'Ninilchik Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfNoatak: Coding = {
  code: '483',
  display: 'Native Village of Noatak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NomeEskimoCommunity: Coding = {
  code: '484',
  display: 'Nome Eskimo Community',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NondaltonVillage: Coding = {
  code: '485',
  display: 'Nondalton Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NoorvikNativeCommunity: Coding = {
  code: '486',
  display: 'Noorvik Native Community',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NorthwayVillage: Coding = {
  code: '487',
  display: 'Northway Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfNuiqsutAkaNooiksut: Coding = {
  code: '488',
  display: 'Native Village of Nuiqsut (aka Nooiksut)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NulatoVillage: Coding = {
  code: '489',
  display: 'Nulato Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CloverdaleRancheriaOfPomoIndiansOfCalifornia: Coding = {
  code: '49',
  display: 'Cloverdale Rancheria of Pomo Indians of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NunakauyarmiutTribeFormerlyNativeVillageOfT: Coding = {
  code: '490',
  display: 'Nunakauyarmiut Tribe (formerly Native Village of T',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfNunapitchuk: Coding = {
  code: '491',
  display: 'Native Village of Nunapitchuk',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfOhogamiut: Coding = {
  code: '492',
  display: 'Village of Ohogamiut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfOldHarbor: Coding = {
  code: '493',
  display: 'Village of Old Harbor',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OrutsararmuitNativeVillageAkaBethel: Coding = {
  code: '494',
  display: 'Orutsararmuit Native Village (aka Bethel)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OscarvilleTraditionalVillage: Coding = {
  code: '495',
  display: 'Oscarville Traditional Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfOuzinkie: Coding = {
  code: '496',
  display: 'Native Village of Ouzinkie',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfPaimiut: Coding = {
  code: '497',
  display: 'Native Village of Paimiut',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PauloffHarborVillage: Coding = {
  code: '498',
  display: 'Pauloff Harbor Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PedroBayVillage: Coding = {
  code: '499',
  display: 'Pedro Bay Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AlabamaQuassarteTribalTownOklahoma: Coding = {
  code: '5',
  display: 'Alabama-Quassarte Tribal Town, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CocopahTribeOfArizona: Coding = {
  code: '50',
  display: 'Cocopah Tribe of Arizona',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfPerryville: Coding = {
  code: '500',
  display: 'Native Village of Perryville',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PetersburgIndianAssociation: Coding = {
  code: '501',
  display: 'Petersburg Indian Association',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfPilotPoint: Coding = {
  code: '502',
  display: 'Native Village of Pilot Point',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PilotStationTraditionalVillage: Coding = {
  code: '503',
  display: 'Pilot Station Traditional Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfPitkaQuoteSPoint: Coding = {
  code: '504',
  display: "Native Village of Pitka's Point",
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PlatinumTraditionalVillage: Coding = {
  code: '505',
  display: 'Platinum Traditional Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfPointHope: Coding = {
  code: '506',
  display: 'Native Village of Point Hope',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfPointLay: Coding = {
  code: '507',
  display: 'Native Village of Point Lay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfPortGraham: Coding = {
  code: '508',
  display: 'Native Village of Port Graham',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfPortHeiden: Coding = {
  code: '509',
  display: 'Native Village of Port Heiden',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CoeurDQuoteAleneTribeOfTheCoeurDQuoteAleneReservati: Coding = {
  code: '51',
  display: "Coeur D'Alene Tribe of the Coeur D'Alene Reservati",
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfPortLions: Coding = {
  code: '510',
  display: 'Native Village of Port Lions',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PortageCreekVillageAkaOhgsenakale: Coding = {
  code: '511',
  display: 'Portage Creek Village (aka Ohgsenakale)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_PribilofIslandsAleutCommunitiesOfStPaulAndS: Coding = {
  code: '512',
  display: 'Pribilof Islands Aleut Communities of St. Paul & S',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_QaganTayagunginTribeOfSandPointVillage: Coding = {
  code: '513',
  display: 'Qagan Tayagungin Tribe of Sand Point Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_QawalanginTribeOfUnalaska: Coding = {
  code: '514',
  display: 'Qawalangin Tribe of Unalaska',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_RampartVillage: Coding = {
  code: '515',
  display: 'Rampart Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfRedDevil: Coding = {
  code: '516',
  display: 'Village of Red Devil',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfRuby: Coding = {
  code: '517',
  display: 'Native Village of Ruby',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SaintGeorgeIslandSeePribilofIslandsAleutCom: Coding = {
  code: '518',
  display: 'Saint George Island(See Pribilof Islands Aleut Com',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfSaintMichael: Coding = {
  code: '519',
  display: 'Native Village of Saint Michael',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ColdSpringsRancheriaOfMonoIndiansOfCaliforn: Coding = {
  code: '52',
  display: 'Cold Springs Rancheria of Mono Indians of Californ',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SaintPaulIslandSeePribilofIslandsAleutComm: Coding = {
  code: '520',
  display: 'Saint Paul Island (See Pribilof Islands Aleut Comm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfSalamatoff: Coding = {
  code: '521',
  display: 'Village of Salamatoff',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfSavoonga: Coding = {
  code: '522',
  display: 'Native Village of Savoonga',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_OrganizedVillageOfSaxman: Coding = {
  code: '523',
  display: 'Organized Village of Saxman',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfScammonBay: Coding = {
  code: '524',
  display: 'Native Village of Scammon Bay',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfSelawik: Coding = {
  code: '525',
  display: 'Native Village of Selawik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SeldoviaVillageTribe: Coding = {
  code: '526',
  display: 'Seldovia Village Tribe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ShagelukNativeVillage: Coding = {
  code: '527',
  display: 'Shageluk Native Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfShaktoolik: Coding = {
  code: '528',
  display: 'Native Village of Shaktoolik',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfSheldonQuoteSPoint: Coding = {
  code: '529',
  display: "Native Village of Sheldon's Point",
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ColoradoRiverIndianTribesOfTheColoradoRiver: Coding = {
  code: '53',
  display: 'Colorado River Indian Tribes of the Colorado River',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfShishmaref: Coding = {
  code: '530',
  display: 'Native Village of Shishmaref',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ShoonaqTribeOfKodiak: Coding = {
  code: '531',
  display: 'Shoonaq Tribe of Kodiak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfShungnak: Coding = {
  code: '532',
  display: 'Native Village of Shungnak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SitkaTribeOfAlaska: Coding = {
  code: '533',
  display: 'Sitka Tribe of Alaska',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SkagwayVillage: Coding = {
  code: '534',
  display: 'Skagway Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfSleetmute: Coding = {
  code: '535',
  display: 'Village of Sleetmute',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfSolomon: Coding = {
  code: '536',
  display: 'Village of Solomon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_SouthNaknekVillage: Coding = {
  code: '537',
  display: 'South Naknek Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_StebbinsCommunityAssociation: Coding = {
  code: '538',
  display: 'Stebbins Community Association',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfStevens: Coding = {
  code: '539',
  display: 'Native Village of Stevens',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ComancheIndianTribeOklahoma: Coding = {
  code: '54',
  display: 'Comanche Indian Tribe, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfStonyRiver: Coding = {
  code: '540',
  display: 'Village of Stony River',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TakotnaVillage: Coding = {
  code: '541',
  display: 'Takotna Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfTanacross: Coding = {
  code: '542',
  display: 'Native Village of Tanacross',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfTanana: Coding = {
  code: '543',
  display: 'Native Village of Tanana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfTatitlek: Coding = {
  code: '544',
  display: 'Native Village of Tatitlek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfTazlina: Coding = {
  code: '545',
  display: 'Native Village of Tazlina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TelidaVillage: Coding = {
  code: '546',
  display: 'Telida Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfTeller: Coding = {
  code: '547',
  display: 'Native Village of Teller',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfTetlin: Coding = {
  code: '548',
  display: 'Native Village of Tetlin',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CentralCouncilOfTheTlingitAndHaidaIndianTb: Coding = {
  code: '549',
  display: 'Central Council of the Tlingit and Haida Indian Tb',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ConfederatedSalishAndKootenaiTribesOfTheFlath: Coding = {
  code: '55',
  display: 'Confederated Salish & Kootenai Tribes of the Flath',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TraditionalVillageOfTogiak: Coding = {
  code: '550',
  display: 'Traditional Village of Togiak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TuluksakNativeCommunity: Coding = {
  code: '551',
  display: 'Tuluksak Native Community',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfTuntutuliak: Coding = {
  code: '552',
  display: 'Native Village of Tuntutuliak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfTununak: Coding = {
  code: '553',
  display: 'Native Village of Tununak',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_TwinHillsVillage: Coding = {
  code: '554',
  display: 'Twin Hills Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfTyonek: Coding = {
  code: '555',
  display: 'Native Village of Tyonek',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_UgashikVillage: Coding = {
  code: '556',
  display: 'Ugashik Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_UmkumiuteNativeVillage: Coding = {
  code: '557',
  display: 'Umkumiute Native Village',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfUnalakleet: Coding = {
  code: '558',
  display: 'Native Village of Unalakleet',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfUnga: Coding = {
  code: '559',
  display: 'Native Village of Unga',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ConfederatedTribesOfTheChehalisReservationW: Coding = {
  code: '56',
  display: 'Confederated Tribes of the Chehalis Reservation, W',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfVenetieSeeNativeVillageOfVenetie: Coding = {
  code: '560',
  display: 'Village of Venetie (See Native Village of Venetie',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfVenetieTribalGovernmentArcti: Coding = {
  code: '561',
  display: 'Native Village of Venetie Tribal Government (Arcti',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_VillageOfWainwright: Coding = {
  code: '562',
  display: 'Village of Wainwright',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfWales: Coding = {
  code: '563',
  display: 'Native Village of Wales',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_NativeVillageOfWhiteMountain: Coding = {
  code: '564',
  display: 'Native Village of White Mountain',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_WrangellCooperativeAssociation: Coding = {
  code: '565',
  display: 'Wrangell Cooperative Association',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_YakutatTlingitTribe: Coding = {
  code: '566',
  display: 'Yakutat Tlingit Tribe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ConfederatedTribesOfTheColvilleReservationW: Coding = {
  code: '57',
  display: 'Confederated Tribes of the Colville Reservation, W',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ConfederatedTribesOfTheCoosLowerUmpquaAnd: Coding = {
  code: '58',
  display: 'Confederated Tribes of the Coos, Lower Umpqua and',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ConfederatedTribesOfTheGoshuteReservationNe: Coding = {
  code: '59',
  display: 'Confederated Tribes of the Goshute Reservation, Ne',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AlturasIndianRancheriaCalifornia: Coding = {
  code: '6',
  display: 'Alturas Indian Rancheria, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ConfederatedTribesOfTheGrandRondeCommunityO: Coding = {
  code: '60',
  display: 'Confederated Tribes of the Grand Ronde Community o',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ConfederatedTribesOfTheSiletzReservationOre: Coding = {
  code: '61',
  display: 'Confederated Tribes of the Siletz Reservation, Ore',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ConfederatedTribesOfTheUmatillaReservationO: Coding = {
  code: '62',
  display: 'Confederated Tribes of the Umatilla Reservation, O',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ConfederatedTribesOfTheWarmSpringsReservatio: Coding = {
  code: '63',
  display: 'Confederated Tribes of the Warm Springs Reservatio',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ConfederatedTribesAndBandsOfTheYakamaIndian: Coding = {
  code: '64',
  display: 'Confederated Tribes and Bands of the Yakama Indian',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CoquilleTribeOfOregon: Coding = {
  code: '65',
  display: 'Coquille Tribe of Oregon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CortinaIndianRancheriaOfWintunIndiansOfCali: Coding = {
  code: '66',
  display: 'Cortina Indian Rancheria of Wintun Indians of Cali',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CoushattaTribeOfLouisiana: Coding = {
  code: '67',
  display: 'Coushatta Tribe of Louisiana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CowCreekBandOfUmpquaIndiansOfOregon: Coding = {
  code: '68',
  display: 'Cow Creek Band of Umpqua Indians of Oregon',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CoyoteValleyBandOfPomoIndiansOfCalifornia: Coding = {
  code: '69',
  display: 'Coyote Valley Band of Pomo Indians of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ApacheTribeOfOklahoma: Coding = {
  code: '7',
  display: 'Apache Tribe of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CrowTribeOfMontana: Coding = {
  code: '70',
  display: 'Crow Tribe of Montana',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CrowCreekSiouxTribeOfTheCrowCreekReservati: Coding = {
  code: '71',
  display: 'Crow Creek Sioux Tribe of the Crow Creek Reservati',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_CuyapaipeCommunityOfDieguenoMissionIndiansOf: Coding = {
  code: '72',
  display: 'Cuyapaipe Community of Diegueno Mission Indians of',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_DeathValleyTimbiShaShoshoneBandOfCalifornia: Coding = {
  code: '73',
  display: 'Death Valley Timbi-Sha Shoshone Band of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_DelawareNationOklahomaFormerlyDelawareTribe: Coding = {
  code: '74',
  display: 'Delaware Nation, Oklahoma (formerly Delaware Tribe',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_DelawareTribeOfIndiansOklahoma: Coding = {
  code: '75',
  display: 'Delaware Tribe of Indians, Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_DryCreekRancheriaOfPomoIndiansOfCalifornia: Coding = {
  code: '76',
  display: 'Dry Creek Rancheria of Pomo Indians of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_DuckwaterShoshoneTribeOfTheDuckwaterReservat: Coding = {
  code: '77',
  display: 'Duckwater Shoshone Tribe of the Duckwater Reservat',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_EasternBandOfCherokeeIndiansOfNorthCarolina: Coding = {
  code: '78',
  display: 'Eastern Band of Cherokee Indians of North Carolina',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_EasternShawneeTribeOfOklahoma: Coding = {
  code: '79',
  display: 'Eastern Shawnee Tribe of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ArapahoeTribeOfTheWindRiverReservationWyom: Coding = {
  code: '8',
  display: 'Arapahoe Tribe of the Wind River Reservation, Wyom',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ElemIndianColonyOfPomoIndiansOfTheSulphur: Coding = {
  code: '80',
  display: 'Elem Indian Colony of Pomo Indians of the Sulphur',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ElkValleyRancheriaCalifornia: Coding = {
  code: '81',
  display: 'Elk Valley Rancheria, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ElyShoshoneTribeOfNevada: Coding = {
  code: '82',
  display: 'Ely Shoshone Tribe of Nevada',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_EnterpriseRancheriaOfMaiduIndiansOfCaliforni: Coding = {
  code: '83',
  display: 'Enterprise Rancheria of Maidu Indians of Californi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_FlandreauSanteeSiouxTribeOfSouthDakota: Coding = {
  code: '84',
  display: 'Flandreau Santee Sioux Tribe of South Dakota',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_ForestCountyPotawatomiCommunityOfWisconsinPo: Coding = {
  code: '85',
  display: 'Forest County Potawatomi Community of Wisconsin Po',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_FortBelknapIndianCommunityOfTheFortBelknap: Coding = {
  code: '86',
  display: 'Fort Belknap Indian Community of the Fort Belknap',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_FortBidwellIndianCommunityOfTheFortBidwell: Coding = {
  code: '87',
  display: 'Fort Bidwell Indian Community of the Fort Bidwell',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_FortIndependenceIndianCommunityOfPaiuteIndia: Coding = {
  code: '88',
  display: 'Fort Independence Indian Community of Paiute India',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_FortMcDermittPaiuteAndShoshoneTribesOfTheF: Coding = {
  code: '89',
  display: 'Fort McDermitt Paiute and Shoshone Tribes of the F',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_AroostookBandOfMicmacIndiansOfMaine: Coding = {
  code: '9',
  display: 'Aroostook Band of Micmac Indians of Maine',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_FortMcDowellYavapaiNationArizonaFormerlyTh: Coding = {
  code: '90',
  display: 'Fort McDowell Yavapai Nation, Arizona (formerly th',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_FortMojaveIndianTribeOfArizonaCalifornia: Coding = {
  code: '91',
  display: 'Fort Mojave Indian Tribe of Arizona, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_FortSillApacheTribeOfOklahoma: Coding = {
  code: '92',
  display: 'Fort Sill Apache Tribe of Oklahoma',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_GilaRiverIndianCommunityOfTheGilaRiverIndi: Coding = {
  code: '93',
  display: 'Gila River Indian Community of the Gila River Indi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_GrandTraverseBandOfOttawaAndChippewaIndiansO: Coding = {
  code: '94',
  display: 'Grand Traverse Band of Ottawa & Chippewa Indians o',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_GratonRancheriaCalifornia: Coding = {
  code: '95',
  display: 'Graton Rancheria, California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_GreenvilleRancheriaOfMaiduIndiansOfCaliforni: Coding = {
  code: '96',
  display: 'Greenville Rancheria of Maidu Indians of Californi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_GrindstoneIndianRancheriaOfWintunWailakiIndi: Coding = {
  code: '97',
  display: 'Grindstone Indian Rancheria of Wintun-Wailaki Indi',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_GuidivilleRancheriaOfCalifornia: Coding = {
  code: '98',
  display: 'Guidiville Rancheria of California',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
const v3_TribalEntityUS_HannahvilleIndianCommunityOfWisconsinPotawato: Coding = {
  code: '99',
  display: 'Hannahville Indian Community of Wisconsin Potawato',
  system: 'http://terminology.hl7.org/CodeSystem/v3-TribalEntityUS',
};
/**
 *  INDIAN ENTITIES RECOGNIZED AND ELIGIBLE TO RECEIVE SERVICES FROM THE UNITED STATES BUREAU OF INDIAN AFFAIRS
 */
export const V3TribalEntityUS = {
  /**
   * NATIVE ENTITIES WITHIN THE STATE OF ALASKA RECOGNIZED AND ELIGIBLE TO RECEIVE SERVICES FROM THE UNITED STATES BUREAU OF INDIAN AFFAIRS
   */
  NativeEntityAlaska: v3_TribalEntityUS_NativeEntityAlaska,
  /**
   * NATIVE ENTITIES WITHIN THE CONTIGUOUS 48 STATES
   */
  NativeEntityContiguous: v3_TribalEntityUS_NativeEntityContiguous,
  /**
   * Absentee-Shawnee Tribe of Indians of Oklahoma
   */
  AbsenteeShawneeTribeOfIndiansOfOklahoma: v3_TribalEntityUS_AbsenteeShawneeTribeOfIndiansOfOklahoma,
  /**
   * Assiniboine and Sioux Tribes of the Fort Peck Indi
   */
  AssiniboineAndSiouxTribesOfTheFortPeckIndi: v3_TribalEntityUS_AssiniboineAndSiouxTribesOfTheFortPeckIndi,
  /**
   * Havasupai Tribe of the Havasupai Reservation, Ariz
   */
  HavasupaiTribeOfTheHavasupaiReservationAriz: v3_TribalEntityUS_HavasupaiTribeOfTheHavasupaiReservationAriz,
  /**
   * Ho-Chunk Nation of Wisconsin (formerly known as th
   */
  HoChunkNationOfWisconsinFormerlyKnownAsTh: v3_TribalEntityUS_HoChunkNationOfWisconsinFormerlyKnownAsTh,
  /**
   * Hoh Indian Tribe of the Hoh Indian Reservation, Wa
   */
  HohIndianTribeOfTheHohIndianReservationWa: v3_TribalEntityUS_HohIndianTribeOfTheHohIndianReservationWa,
  /**
   * Hoopa Valley Tribe, California
   */
  HoopaValleyTribeCalifornia: v3_TribalEntityUS_HoopaValleyTribeCalifornia,
  /**
   * Hopi Tribe of Arizona
   */
  HopiTribeOfArizona: v3_TribalEntityUS_HopiTribeOfArizona,
  /**
   * Hopland Band of Pomo Indians of the Hopland Ranche
   */
  HoplandBandOfPomoIndiansOfTheHoplandRanche: v3_TribalEntityUS_HoplandBandOfPomoIndiansOfTheHoplandRanche,
  /**
   * Houlton Band of Maliseet Indians of Maine
   */
  HoultonBandOfMaliseetIndiansOfMaine: v3_TribalEntityUS_HoultonBandOfMaliseetIndiansOfMaine,
  /**
   * Hualapai Indian Tribe of the Hualapai Indian Reser
   */
  HualapaiIndianTribeOfTheHualapaiIndianReser: v3_TribalEntityUS_HualapaiIndianTribeOfTheHualapaiIndianReser,
  /**
   * Huron Potawatomi, Inc., Michigan
   */
  HuronPotawatomiIncMichigan: v3_TribalEntityUS_HuronPotawatomiIncMichigan,
  /**
   * Inaja Band of Diegueno Mission Indians of the Inaj
   */
  InajaBandOfDieguenoMissionIndiansOfTheInaj: v3_TribalEntityUS_InajaBandOfDieguenoMissionIndiansOfTheInaj,
  /**
   * Augustine Band of Cahuilla Mission Indians of the
   */
  AugustineBandOfCahuillaMissionIndiansOfThe: v3_TribalEntityUS_AugustineBandOfCahuillaMissionIndiansOfThe,
  /**
   * Ione Band of Miwok Indians of California
   */
  IoneBandOfMiwokIndiansOfCalifornia: v3_TribalEntityUS_IoneBandOfMiwokIndiansOfCalifornia,
  /**
   * Iowa Tribe of Kansas and Nebraska
   */
  IowaTribeOfKansasAndNebraska: v3_TribalEntityUS_IowaTribeOfKansasAndNebraska,
  /**
   * Iowa Tribe of Oklahoma
   */
  IowaTribeOfOklahoma: v3_TribalEntityUS_IowaTribeOfOklahoma,
  /**
   * Jackson Rancheria of Me-Wuk Indians of California
   */
  JacksonRancheriaOfMeWukIndiansOfCalifornia: v3_TribalEntityUS_JacksonRancheriaOfMeWukIndiansOfCalifornia,
  /**
   * Jamestown S'Klallam Tribe of Washington
   */
  JamestownSQuoteKlallamTribeOfWashington: v3_TribalEntityUS_JamestownSQuoteKlallamTribeOfWashington,
  /**
   * Jamul Indian Village of California
   */
  JamulIndianVillageOfCalifornia: v3_TribalEntityUS_JamulIndianVillageOfCalifornia,
  /**
   * Jena Band of Choctaw Indians, Louisiana
   */
  JenaBandOfChoctawIndiansLouisiana: v3_TribalEntityUS_JenaBandOfChoctawIndiansLouisiana,
  /**
   * Jicarilla Apache Tribe of the Jicarilla Apache Ind
   */
  JicarillaApacheTribeOfTheJicarillaApacheInd: v3_TribalEntityUS_JicarillaApacheTribeOfTheJicarillaApacheInd,
  /**
   * Kaibab Band of Paiute Indians of the Kaibab Indian
   */
  KaibabBandOfPaiuteIndiansOfTheKaibabIndian: v3_TribalEntityUS_KaibabBandOfPaiuteIndiansOfTheKaibabIndian,
  /**
   * Kalispel Indian Community of the Kalispel Reservat
   */
  KalispelIndianCommunityOfTheKalispelReservat: v3_TribalEntityUS_KalispelIndianCommunityOfTheKalispelReservat,
  /**
   * Bad River Band of the Lake Superior Tribe of Chipp
   */
  BadRiverBandOfTheLakeSuperiorTribeOfChipp: v3_TribalEntityUS_BadRiverBandOfTheLakeSuperiorTribeOfChipp,
  /**
   * Karuk Tribe of California
   */
  KarukTribeOfCalifornia: v3_TribalEntityUS_KarukTribeOfCalifornia,
  /**
   * Kashia Band of Pomo Indians of the Stewarts Point
   */
  KashiaBandOfPomoIndiansOfTheStewartsPoint: v3_TribalEntityUS_KashiaBandOfPomoIndiansOfTheStewartsPoint,
  /**
   * Kaw Nation, Oklahoma
   */
  KawNationOklahoma: v3_TribalEntityUS_KawNationOklahoma,
  /**
   * Keweenaw Bay Indian Community of L'Anse and Ontona
   */
  KeweenawBayIndianCommunityOfLQuoteAnseAndOntona: v3_TribalEntityUS_KeweenawBayIndianCommunityOfLQuoteAnseAndOntona,
  /**
   * Kialegee Tribal Town, Oklahoma
   */
  KialegeeTribalTownOklahoma: v3_TribalEntityUS_KialegeeTribalTownOklahoma,
  /**
   * Kickapoo Tribe of Indians of the Kickapoo Reservat
   */
  KickapooTribeOfIndiansOfTheKickapooReservat: v3_TribalEntityUS_KickapooTribeOfIndiansOfTheKickapooReservat,
  /**
   * Kickapoo Tribe of Oklahoma
   */
  KickapooTribeOfOklahoma: v3_TribalEntityUS_KickapooTribeOfOklahoma,
  /**
   * Kickapoo Traditional Tribe of Texas
   */
  KickapooTraditionalTribeOfTexas: v3_TribalEntityUS_KickapooTraditionalTribeOfTexas,
  /**
   * Kiowa Indian Tribe of Oklahoma
   */
  KiowaIndianTribeOfOklahoma: v3_TribalEntityUS_KiowaIndianTribeOfOklahoma,
  /**
   * Klamath Indian Tribe of Oregon
   */
  KlamathIndianTribeOfOregon: v3_TribalEntityUS_KlamathIndianTribeOfOregon,
  /**
   * Bay Mills Indian Community of the Sault Ste. Marie
   */
  BayMillsIndianCommunityOfTheSaultSteMarie: v3_TribalEntityUS_BayMillsIndianCommunityOfTheSaultSteMarie,
  /**
   * Kootenai Tribe of Idaho
   */
  KootenaiTribeOfIdaho: v3_TribalEntityUS_KootenaiTribeOfIdaho,
  /**
   * La Jolla Band of Luiseno Mission Indians of the La
   */
  LaJollaBandOfLuisenoMissionIndiansOfTheLa: v3_TribalEntityUS_LaJollaBandOfLuisenoMissionIndiansOfTheLa,
  /**
   * La Posta Band of Diegueno Mission Indians of the L
   */
  LaPostaBandOfDieguenoMissionIndiansOfTheL: v3_TribalEntityUS_LaPostaBandOfDieguenoMissionIndiansOfTheL,
  /**
   * Lac Courte Oreilles Band of Lake Superior Chippewa
   */
  LacCourteOreillesBandOfLakeSuperiorChippewa: v3_TribalEntityUS_LacCourteOreillesBandOfLakeSuperiorChippewa,
  /**
   * Lac du Flambeau Band of Lake Superior Chippewa Ind
   */
  LacDuFlambeauBandOfLakeSuperiorChippewaInd: v3_TribalEntityUS_LacDuFlambeauBandOfLakeSuperiorChippewaInd,
  /**
   * Lac Vieux Desert Band of Lake Superior Chippewa In
   */
  LacVieuxDesertBandOfLakeSuperiorChippewaIn: v3_TribalEntityUS_LacVieuxDesertBandOfLakeSuperiorChippewaIn,
  /**
   * Las Vegas Tribe of Paiute Indians of the Las Vegas
   */
  LasVegasTribeOfPaiuteIndiansOfTheLasVegas: v3_TribalEntityUS_LasVegasTribeOfPaiuteIndiansOfTheLasVegas,
  /**
   * Little River Band of Ottawa Indians of Michigan
   */
  LittleRiverBandOfOttawaIndiansOfMichigan: v3_TribalEntityUS_LittleRiverBandOfOttawaIndiansOfMichigan,
  /**
   * Little Traverse Bay Bands of Odawa Indians of Mich
   */
  LittleTraverseBayBandsOfOdawaIndiansOfMich: v3_TribalEntityUS_LittleTraverseBayBandsOfOdawaIndiansOfMich,
  /**
   * Lower Lake Rancheria, California
   */
  LowerLakeRancheriaCalifornia: v3_TribalEntityUS_LowerLakeRancheriaCalifornia,
  /**
   * Bear River Band of the Rohnerville Rancheria, Cali
   */
  BearRiverBandOfTheRohnervilleRancheriaCali: v3_TribalEntityUS_BearRiverBandOfTheRohnervilleRancheriaCali,
  /**
   * Los Coyotes Band of Cahuilla Mission Indians of th
   */
  LosCoyotesBandOfCahuillaMissionIndiansOfTh: v3_TribalEntityUS_LosCoyotesBandOfCahuillaMissionIndiansOfTh,
  /**
   * Lovelock Paiute Tribe of the Lovelock Indian Colon
   */
  LovelockPaiuteTribeOfTheLovelockIndianColon: v3_TribalEntityUS_LovelockPaiuteTribeOfTheLovelockIndianColon,
  /**
   * Lower Brule Sioux Tribe of the Lower Brule Reserva
   */
  LowerBruleSiouxTribeOfTheLowerBruleReserva: v3_TribalEntityUS_LowerBruleSiouxTribeOfTheLowerBruleReserva,
  /**
   * Lower Elwha Tribal Community of the Lower Elwha Re
   */
  LowerElwhaTribalCommunityOfTheLowerElwhaRe: v3_TribalEntityUS_LowerElwhaTribalCommunityOfTheLowerElwhaRe,
  /**
   * Lower Sioux Indian Community of Minnesota Mdewakan
   */
  LowerSiouxIndianCommunityOfMinnesotaMdewakan: v3_TribalEntityUS_LowerSiouxIndianCommunityOfMinnesotaMdewakan,
  /**
   * Lummi Tribe of the Lummi Reservation, Washington
   */
  LummiTribeOfTheLummiReservationWashington: v3_TribalEntityUS_LummiTribeOfTheLummiReservationWashington,
  /**
   * Lytton Rancheria of California
   */
  LyttonRancheriaOfCalifornia: v3_TribalEntityUS_LyttonRancheriaOfCalifornia,
  /**
   * Makah Indian Tribe of the Makah Indian Reservation
   */
  MakahIndianTribeOfTheMakahIndianReservation: v3_TribalEntityUS_MakahIndianTribeOfTheMakahIndianReservation,
  /**
   * Manchester Band of Pomo Indians of the Manchester-
   */
  ManchesterBandOfPomoIndiansOfTheManchester: v3_TribalEntityUS_ManchesterBandOfPomoIndiansOfTheManchester,
  /**
   * Manzanita Band of Diegueno Mission Indians of the
   */
  ManzanitaBandOfDieguenoMissionIndiansOfThe: v3_TribalEntityUS_ManzanitaBandOfDieguenoMissionIndiansOfThe,
  /**
   * Berry Creek Rancheria of Maidu Indians of Californ
   */
  BerryCreekRancheriaOfMaiduIndiansOfCaliforn: v3_TribalEntityUS_BerryCreekRancheriaOfMaiduIndiansOfCaliforn,
  /**
   * Mashantucket Pequot Tribe of Connecticut
   */
  MashantucketPequotTribeOfConnecticut: v3_TribalEntityUS_MashantucketPequotTribeOfConnecticut,
  /**
   * Match-e-be-nash-she-wish Band of Pottawatomi India
   */
  MatchEBeNashSheWishBandOfPottawatomiIndia: v3_TribalEntityUS_MatchEBeNashSheWishBandOfPottawatomiIndia,
  /**
   * Mechoopda Indian Tribe of Chico Rancheria, Califor
   */
  MechoopdaIndianTribeOfChicoRancheriaCalifor: v3_TribalEntityUS_MechoopdaIndianTribeOfChicoRancheriaCalifor,
  /**
   * Menominee Indian Tribe of Wisconsin
   */
  MenomineeIndianTribeOfWisconsin: v3_TribalEntityUS_MenomineeIndianTribeOfWisconsin,
  /**
   * Mesa Grande Band of Diegueno Mission Indians of th
   */
  MesaGrandeBandOfDieguenoMissionIndiansOfTh: v3_TribalEntityUS_MesaGrandeBandOfDieguenoMissionIndiansOfTh,
  /**
   * Mescalero Apache Tribe of the Mescalero Reservatio
   */
  MescaleroApacheTribeOfTheMescaleroReservatio: v3_TribalEntityUS_MescaleroApacheTribeOfTheMescaleroReservatio,
  /**
   * Miami Tribe of Oklahoma
   */
  MiamiTribeOfOklahoma: v3_TribalEntityUS_MiamiTribeOfOklahoma,
  /**
   * Miccosukee Tribe of Indians of Florida
   */
  MiccosukeeTribeOfIndiansOfFlorida: v3_TribalEntityUS_MiccosukeeTribeOfIndiansOfFlorida,
  /**
   * Middletown Rancheria of Pomo Indians of California
   */
  MiddletownRancheriaOfPomoIndiansOfCalifornia: v3_TribalEntityUS_MiddletownRancheriaOfPomoIndiansOfCalifornia,
  /**
   * Minnesota Chippewa Tribe, Minnesota (Six component
   */
  MinnesotaChippewaTribeMinnesotaSixComponent: v3_TribalEntityUS_MinnesotaChippewaTribeMinnesotaSixComponent,
  /**
   * Big Lagoon Rancheria, California
   */
  BigLagoonRancheriaCalifornia: v3_TribalEntityUS_BigLagoonRancheriaCalifornia,
  /**
   * Bois Forte Band (Nett Lake); Fond du Lac Band; Gra
   */
  BoisForteBandNettLakeFondDuLacBandGra: v3_TribalEntityUS_BoisForteBandNettLakeFondDuLacBandGra,
  /**
   * Mississippi Band of Choctaw Indians, Mississippi
   */
  MississippiBandOfChoctawIndiansMississippi: v3_TribalEntityUS_MississippiBandOfChoctawIndiansMississippi,
  /**
   * Moapa Band of Paiute Indians of the Moapa River In
   */
  MoapaBandOfPaiuteIndiansOfTheMoapaRiverIn: v3_TribalEntityUS_MoapaBandOfPaiuteIndiansOfTheMoapaRiverIn,
  /**
   * Modoc Tribe of Oklahoma
   */
  ModocTribeOfOklahoma: v3_TribalEntityUS_ModocTribeOfOklahoma,
  /**
   * Mohegan Indian Tribe of Connecticut
   */
  MoheganIndianTribeOfConnecticut: v3_TribalEntityUS_MoheganIndianTribeOfConnecticut,
  /**
   * Mooretown Rancheria of Maidu Indians of California
   */
  MooretownRancheriaOfMaiduIndiansOfCalifornia: v3_TribalEntityUS_MooretownRancheriaOfMaiduIndiansOfCalifornia,
  /**
   * Morongo Band of Cahuilla Mission Indians of the Mo
   */
  MorongoBandOfCahuillaMissionIndiansOfTheMo: v3_TribalEntityUS_MorongoBandOfCahuillaMissionIndiansOfTheMo,
  /**
   * Muckleshoot Indian Tribe of the Muckleshoot Reserv
   */
  MuckleshootIndianTribeOfTheMuckleshootReserv: v3_TribalEntityUS_MuckleshootIndianTribeOfTheMuckleshootReserv,
  /**
   * Muscogee (Creek) Nation, Oklahoma
   */
  MuscogeeCreekNationOklahoma: v3_TribalEntityUS_MuscogeeCreekNationOklahoma,
  /**
   * Narragansett Indian Tribe of Rhode Island
   */
  NarragansettIndianTribeOfRhodeIsland: v3_TribalEntityUS_NarragansettIndianTribeOfRhodeIsland,
  /**
   * Big Pine Band of Owens Valley Paiute Shoshone Indi
   */
  BigPineBandOfOwensValleyPaiuteShoshoneIndi: v3_TribalEntityUS_BigPineBandOfOwensValleyPaiuteShoshoneIndi,
  /**
   * Navajo Nation, Arizona, New Mexico & Utah
   */
  NavajoNationArizonaNewMexicoAndUtah: v3_TribalEntityUS_NavajoNationArizonaNewMexicoAndUtah,
  /**
   * Nez Perce Tribe of Idaho
   */
  NezPerceTribeOfIdaho: v3_TribalEntityUS_NezPerceTribeOfIdaho,
  /**
   * Nisqually Indian Tribe of the Nisqually Reservatio
   */
  NisquallyIndianTribeOfTheNisquallyReservatio: v3_TribalEntityUS_NisquallyIndianTribeOfTheNisquallyReservatio,
  /**
   * Nooksack Indian Tribe of Washington
   */
  NooksackIndianTribeOfWashington: v3_TribalEntityUS_NooksackIndianTribeOfWashington,
  /**
   * Northern Cheyenne Tribe of the Northern Cheyenne I
   */
  NorthernCheyenneTribeOfTheNorthernCheyenneI: v3_TribalEntityUS_NorthernCheyenneTribeOfTheNorthernCheyenneI,
  /**
   * Northfork Rancheria of Mono Indians of California
   */
  NorthforkRancheriaOfMonoIndiansOfCalifornia: v3_TribalEntityUS_NorthforkRancheriaOfMonoIndiansOfCalifornia,
  /**
   * Northwestern Band of Shoshoni Nation of Utah (Wash
   */
  NorthwesternBandOfShoshoniNationOfUtahWash: v3_TribalEntityUS_NorthwesternBandOfShoshoniNationOfUtahWash,
  /**
   * Oglala Sioux Tribe of the Pine Ridge Reservation,
   */
  OglalaSiouxTribeOfThePineRidgeReservation: v3_TribalEntityUS_OglalaSiouxTribeOfThePineRidgeReservation,
  /**
   * Omaha Tribe of Nebraska
   */
  OmahaTribeOfNebraska: v3_TribalEntityUS_OmahaTribeOfNebraska,
  /**
   * Oneida Nation of New York
   */
  OneidaNationOfNewYork: v3_TribalEntityUS_OneidaNationOfNewYork,
  /**
   * Big Sandy Rancheria of Mono Indians of California
   */
  BigSandyRancheriaOfMonoIndiansOfCalifornia: v3_TribalEntityUS_BigSandyRancheriaOfMonoIndiansOfCalifornia,
  /**
   * Oneida Tribe of Wisconsin
   */
  OneidaTribeOfWisconsin: v3_TribalEntityUS_OneidaTribeOfWisconsin,
  /**
   * Onondaga Nation of New York
   */
  OnondagaNationOfNewYork: v3_TribalEntityUS_OnondagaNationOfNewYork,
  /**
   * Osage Tribe, Oklahoma
   */
  OsageTribeOklahoma: v3_TribalEntityUS_OsageTribeOklahoma,
  /**
   * Ottawa Tribe of Oklahoma
   */
  OttawaTribeOfOklahoma: v3_TribalEntityUS_OttawaTribeOfOklahoma,
  /**
   * Otoe-Missouria Tribe of Indians, Oklahoma
   */
  OtoeMissouriaTribeOfIndiansOklahoma: v3_TribalEntityUS_OtoeMissouriaTribeOfIndiansOklahoma,
  /**
   * Paiute Indian Tribe of Utah
   */
  PaiuteIndianTribeOfUtah: v3_TribalEntityUS_PaiuteIndianTribeOfUtah,
  /**
   * Paiute-Shoshone Indians of the Bishop Community of
   */
  PaiuteShoshoneIndiansOfTheBishopCommunityOf: v3_TribalEntityUS_PaiuteShoshoneIndiansOfTheBishopCommunityOf,
  /**
   * Paiute-Shoshone Tribe of the Fallon Reservation an
   */
  PaiuteShoshoneTribeOfTheFallonReservationAn: v3_TribalEntityUS_PaiuteShoshoneTribeOfTheFallonReservationAn,
  /**
   * Paiute-Shoshone Indians of the Lone Pine Community
   */
  PaiuteShoshoneIndiansOfTheLonePineCommunity: v3_TribalEntityUS_PaiuteShoshoneIndiansOfTheLonePineCommunity,
  /**
   * Pala Band of Luiseno Mission Indians of the Pala R
   */
  PalaBandOfLuisenoMissionIndiansOfThePalaR: v3_TribalEntityUS_PalaBandOfLuisenoMissionIndiansOfThePalaR,
  /**
   * Big Valley Band of Pomo Indians of the Big Valley
   */
  BigValleyBandOfPomoIndiansOfTheBigValley: v3_TribalEntityUS_BigValleyBandOfPomoIndiansOfTheBigValley,
  /**
   * Pascua Yaqui Tribe of Arizona
   */
  PascuaYaquiTribeOfArizona: v3_TribalEntityUS_PascuaYaquiTribeOfArizona,
  /**
   * Paskenta Band of Nomlaki Indians of California
   */
  PaskentaBandOfNomlakiIndiansOfCalifornia: v3_TribalEntityUS_PaskentaBandOfNomlakiIndiansOfCalifornia,
  /**
   * Passamaquoddy Tribe of Maine
   */
  PassamaquoddyTribeOfMaine: v3_TribalEntityUS_PassamaquoddyTribeOfMaine,
  /**
   * Pauma Band of Luiseno Mission Indians of the Pauma
   */
  PaumaBandOfLuisenoMissionIndiansOfThePauma: v3_TribalEntityUS_PaumaBandOfLuisenoMissionIndiansOfThePauma,
  /**
   * Pawnee Nation of Oklahoma
   */
  PawneeNationOfOklahoma: v3_TribalEntityUS_PawneeNationOfOklahoma,
  /**
   * Pechanga Band of Luiseno Mission Indians of the Pe
   */
  PechangaBandOfLuisenoMissionIndiansOfThePe: v3_TribalEntityUS_PechangaBandOfLuisenoMissionIndiansOfThePe,
  /**
   * Penobscot Tribe of Maine
   */
  PenobscotTribeOfMaine: v3_TribalEntityUS_PenobscotTribeOfMaine,
  /**
   * Peoria Tribe of Indians of Oklahoma
   */
  PeoriaTribeOfIndiansOfOklahoma: v3_TribalEntityUS_PeoriaTribeOfIndiansOfOklahoma,
  /**
   * Picayune Rancheria of Chukchansi Indians of Califo
   */
  PicayuneRancheriaOfChukchansiIndiansOfCalifo: v3_TribalEntityUS_PicayuneRancheriaOfChukchansiIndiansOfCalifo,
  /**
   * Pinoleville Rancheria of Pomo Indians of Californi
   */
  PinolevilleRancheriaOfPomoIndiansOfCaliforni: v3_TribalEntityUS_PinolevilleRancheriaOfPomoIndiansOfCaliforni,
  /**
   * Agua Caliente Band of Cahuilla Indians of the Agua
   */
  AguaCalienteBandOfCahuillaIndiansOfTheAgua: v3_TribalEntityUS_AguaCalienteBandOfCahuillaIndiansOfTheAgua,
  /**
   * Blackfeet Tribe of the Blackfeet Indian Reservatio
   */
  BlackfeetTribeOfTheBlackfeetIndianReservatio: v3_TribalEntityUS_BlackfeetTribeOfTheBlackfeetIndianReservatio,
  /**
   * Pit River Tribe, California (includes Big Bend, Lo
   */
  PitRiverTribeCaliforniaIncludesBigBendLo: v3_TribalEntityUS_PitRiverTribeCaliforniaIncludesBigBendLo,
  /**
   * Poarch Band of Creek Indians of Alabama
   */
  PoarchBandOfCreekIndiansOfAlabama: v3_TribalEntityUS_PoarchBandOfCreekIndiansOfAlabama,
  /**
   * Pokagon Band of Potawatomi Indians of Michigan
   */
  PokagonBandOfPotawatomiIndiansOfMichigan: v3_TribalEntityUS_PokagonBandOfPotawatomiIndiansOfMichigan,
  /**
   * Ponca Tribe of Indians of Oklahoma
   */
  PoncaTribeOfIndiansOfOklahoma: v3_TribalEntityUS_PoncaTribeOfIndiansOfOklahoma,
  /**
   * Ponca Tribe of Nebraska
   */
  PoncaTribeOfNebraska: v3_TribalEntityUS_PoncaTribeOfNebraska,
  /**
   * Port Gamble Indian Community of the Port Gamble Re
   */
  PortGambleIndianCommunityOfThePortGambleRe: v3_TribalEntityUS_PortGambleIndianCommunityOfThePortGambleRe,
  /**
   * Potter Valley Rancheria of Pomo Indians of Califor
   */
  PotterValleyRancheriaOfPomoIndiansOfCalifor: v3_TribalEntityUS_PotterValleyRancheriaOfPomoIndiansOfCalifor,
  /**
   * Prairie Band of Potawatomi Indians, Kansas
   */
  PrairieBandOfPotawatomiIndiansKansas: v3_TribalEntityUS_PrairieBandOfPotawatomiIndiansKansas,
  /**
   * Prairie Island Indian Community of Minnesota Mdewa
   */
  PrairieIslandIndianCommunityOfMinnesotaMdewa: v3_TribalEntityUS_PrairieIslandIndianCommunityOfMinnesotaMdewa,
  /**
   * Pueblo of Acoma, New Mexico
   */
  PuebloOfAcomaNewMexico: v3_TribalEntityUS_PuebloOfAcomaNewMexico,
  /**
   * Blue Lake Rancheria, California
   */
  BlueLakeRancheriaCalifornia: v3_TribalEntityUS_BlueLakeRancheriaCalifornia,
  /**
   * Pueblo of Cochiti, New Mexico
   */
  PuebloOfCochitiNewMexico: v3_TribalEntityUS_PuebloOfCochitiNewMexico,
  /**
   * Pueblo of Jemez, New Mexico
   */
  PuebloOfJemezNewMexico: v3_TribalEntityUS_PuebloOfJemezNewMexico,
  /**
   * Pueblo of Isleta, New Mexico
   */
  PuebloOfIsletaNewMexico: v3_TribalEntityUS_PuebloOfIsletaNewMexico,
  /**
   * Pueblo of Laguna, New Mexico
   */
  PuebloOfLagunaNewMexico: v3_TribalEntityUS_PuebloOfLagunaNewMexico,
  /**
   * Pueblo of Nambe, New Mexico
   */
  PuebloOfNambeNewMexico: v3_TribalEntityUS_PuebloOfNambeNewMexico,
  /**
   * Pueblo of Picuris, New Mexico
   */
  PuebloOfPicurisNewMexico: v3_TribalEntityUS_PuebloOfPicurisNewMexico,
  /**
   * Pueblo of Pojoaque, New Mexico
   */
  PuebloOfPojoaqueNewMexico: v3_TribalEntityUS_PuebloOfPojoaqueNewMexico,
  /**
   * Pueblo of San Felipe, New Mexico
   */
  PuebloOfSanFelipeNewMexico: v3_TribalEntityUS_PuebloOfSanFelipeNewMexico,
  /**
   * Pueblo of San Juan, New Mexico
   */
  PuebloOfSanJuanNewMexico: v3_TribalEntityUS_PuebloOfSanJuanNewMexico,
  /**
   * Pueblo of San Ildefonso, New Mexico
   */
  PuebloOfSanIldefonsoNewMexico: v3_TribalEntityUS_PuebloOfSanIldefonsoNewMexico,
  /**
   * Bridgeport Paiute Indian Colony of California
   */
  BridgeportPaiuteIndianColonyOfCalifornia: v3_TribalEntityUS_BridgeportPaiuteIndianColonyOfCalifornia,
  /**
   * Pueblo of Sandia, New Mexico
   */
  PuebloOfSandiaNewMexico: v3_TribalEntityUS_PuebloOfSandiaNewMexico,
  /**
   * Pueblo of Santa Ana, New Mexico
   */
  PuebloOfSantaAnaNewMexico: v3_TribalEntityUS_PuebloOfSantaAnaNewMexico,
  /**
   * Pueblo of Santa Clara, New Mexico
   */
  PuebloOfSantaClaraNewMexico: v3_TribalEntityUS_PuebloOfSantaClaraNewMexico,
  /**
   * Pueblo of Santo Domingo, New Mexico
   */
  PuebloOfSantoDomingoNewMexico: v3_TribalEntityUS_PuebloOfSantoDomingoNewMexico,
  /**
   * Pueblo of Taos, New Mexico
   */
  PuebloOfTaosNewMexico: v3_TribalEntityUS_PuebloOfTaosNewMexico,
  /**
   * Pueblo of Tesuque, New Mexico
   */
  PuebloOfTesuqueNewMexico: v3_TribalEntityUS_PuebloOfTesuqueNewMexico,
  /**
   * Pueblo of Zia, New Mexico
   */
  PuebloOfZiaNewMexico: v3_TribalEntityUS_PuebloOfZiaNewMexico,
  /**
   * Puyallup Tribe of the Puyallup Reservation, Washin
   */
  PuyallupTribeOfThePuyallupReservationWashin: v3_TribalEntityUS_PuyallupTribeOfThePuyallupReservationWashin,
  /**
   * Pyramid Lake Paiute Tribe of the Pyramid Lake Rese
   */
  PyramidLakePaiuteTribeOfThePyramidLakeRese: v3_TribalEntityUS_PyramidLakePaiuteTribeOfThePyramidLakeRese,
  /**
   * Quapaw Tribe of Indians, Oklahoma
   */
  QuapawTribeOfIndiansOklahoma: v3_TribalEntityUS_QuapawTribeOfIndiansOklahoma,
  /**
   * Buena Vista Rancheria of Me-Wuk Indians of Califor
   */
  BuenaVistaRancheriaOfMeWukIndiansOfCalifor: v3_TribalEntityUS_BuenaVistaRancheriaOfMeWukIndiansOfCalifor,
  /**
   * Quartz Valley Indian Community of the Quartz Valle
   */
  QuartzValleyIndianCommunityOfTheQuartzValle: v3_TribalEntityUS_QuartzValleyIndianCommunityOfTheQuartzValle,
  /**
   * Quechan Tribe of the Fort Yuma Indian Reservation,
   */
  QuechanTribeOfTheFortYumaIndianReservation: v3_TribalEntityUS_QuechanTribeOfTheFortYumaIndianReservation,
  /**
   * Quileute Tribe of the Quileute Reservation, Washin
   */
  QuileuteTribeOfTheQuileuteReservationWashin: v3_TribalEntityUS_QuileuteTribeOfTheQuileuteReservationWashin,
  /**
   * Quinault Tribe of the Quinault Reservation, Washin
   */
  QuinaultTribeOfTheQuinaultReservationWashin: v3_TribalEntityUS_QuinaultTribeOfTheQuinaultReservationWashin,
  /**
   * Ramona Band or Village of Cahuilla Mission Indians
   */
  RamonaBandOrVillageOfCahuillaMissionIndians: v3_TribalEntityUS_RamonaBandOrVillageOfCahuillaMissionIndians,
  /**
   * Red Cliff Band of Lake Superior Chippewa Indians o
   */
  RedCliffBandOfLakeSuperiorChippewaIndiansO: v3_TribalEntityUS_RedCliffBandOfLakeSuperiorChippewaIndiansO,
  /**
   * Red Lake Band of Chippewa Indians of the Red Lake
   */
  RedLakeBandOfChippewaIndiansOfTheRedLake: v3_TribalEntityUS_RedLakeBandOfChippewaIndiansOfTheRedLake,
  /**
   * Redding Rancheria, California
   */
  ReddingRancheriaCalifornia: v3_TribalEntityUS_ReddingRancheriaCalifornia,
  /**
   * Redwood Valley Rancheria of Pomo Indians of Califo
   */
  RedwoodValleyRancheriaOfPomoIndiansOfCalifo: v3_TribalEntityUS_RedwoodValleyRancheriaOfPomoIndiansOfCalifo,
  /**
   * Reno-Sparks Indian Colony, Nevada
   */
  RenoSparksIndianColonyNevada: v3_TribalEntityUS_RenoSparksIndianColonyNevada,
  /**
   * Burns Paiute Tribe of the Burns Paiute Indian Colo
   */
  BurnsPaiuteTribeOfTheBurnsPaiuteIndianColo: v3_TribalEntityUS_BurnsPaiuteTribeOfTheBurnsPaiuteIndianColo,
  /**
   * Resighini Rancheria, California (formerly known as
   */
  ResighiniRancheriaCaliforniaFormerlyKnownAs: v3_TribalEntityUS_ResighiniRancheriaCaliforniaFormerlyKnownAs,
  /**
   * Rincon Band of Luiseno Mission Indians of the Rinc
   */
  RinconBandOfLuisenoMissionIndiansOfTheRinc: v3_TribalEntityUS_RinconBandOfLuisenoMissionIndiansOfTheRinc,
  /**
   * Robinson Rancheria of Pomo Indians of California
   */
  RobinsonRancheriaOfPomoIndiansOfCalifornia: v3_TribalEntityUS_RobinsonRancheriaOfPomoIndiansOfCalifornia,
  /**
   * Rosebud Sioux Tribe of the Rosebud Indian Reservat
   */
  RosebudSiouxTribeOfTheRosebudIndianReservat: v3_TribalEntityUS_RosebudSiouxTribeOfTheRosebudIndianReservat,
  /**
   * Round Valley Indian Tribes of the Round Valley Res
   */
  RoundValleyIndianTribesOfTheRoundValleyRes: v3_TribalEntityUS_RoundValleyIndianTribesOfTheRoundValleyRes,
  /**
   * Rumsey Indian Rancheria of Wintun Indians of Calif
   */
  RumseyIndianRancheriaOfWintunIndiansOfCalif: v3_TribalEntityUS_RumseyIndianRancheriaOfWintunIndiansOfCalif,
  /**
   * Sac and Fox Tribe of the Mississippi in Iowa
   */
  SacAndFoxTribeOfTheMississippiInIowa: v3_TribalEntityUS_SacAndFoxTribeOfTheMississippiInIowa,
  /**
   * Sac and Fox Nation of Missouri in Kansas and Nebra
   */
  SacAndFoxNationOfMissouriInKansasAndNebra: v3_TribalEntityUS_SacAndFoxNationOfMissouriInKansasAndNebra,
  /**
   * Sac and Fox Nation, Oklahoma
   */
  SacAndFoxNationOklahoma: v3_TribalEntityUS_SacAndFoxNationOklahoma,
  /**
   * Saginaw Chippewa Indian Tribe of Michigan, Isabell
   */
  SaginawChippewaIndianTribeOfMichiganIsabell: v3_TribalEntityUS_SaginawChippewaIndianTribeOfMichiganIsabell,
  /**
   * Cabazon Band of Cahuilla Mission Indians of the Ca
   */
  CabazonBandOfCahuillaMissionIndiansOfTheCa: v3_TribalEntityUS_CabazonBandOfCahuillaMissionIndiansOfTheCa,
  /**
   * Salt River Pima-Maricopa Indian Community of the S
   */
  SaltRiverPimaMaricopaIndianCommunityOfTheS: v3_TribalEntityUS_SaltRiverPimaMaricopaIndianCommunityOfTheS,
  /**
   * Samish Indian Tribe, Washington
   */
  SamishIndianTribeWashington: v3_TribalEntityUS_SamishIndianTribeWashington,
  /**
   * San Carlos Apache Tribe of the San Carlos Reservat
   */
  SanCarlosApacheTribeOfTheSanCarlosReservat: v3_TribalEntityUS_SanCarlosApacheTribeOfTheSanCarlosReservat,
  /**
   * San Juan Southern Paiute Tribe of Arizona
   */
  SanJuanSouthernPaiuteTribeOfArizona: v3_TribalEntityUS_SanJuanSouthernPaiuteTribeOfArizona,
  /**
   * San Manual Band of Serrano Mission Indians of the
   */
  SanManualBandOfSerranoMissionIndiansOfThe: v3_TribalEntityUS_SanManualBandOfSerranoMissionIndiansOfThe,
  /**
   * San Pasqual Band of Diegueno Mission Indians of Ca
   */
  SanPasqualBandOfDieguenoMissionIndiansOfCa: v3_TribalEntityUS_SanPasqualBandOfDieguenoMissionIndiansOfCa,
  /**
   * Santa Rosa Indian Community of the Santa Rosa Ranc
   */
  SantaRosaIndianCommunityOfTheSantaRosaRanc: v3_TribalEntityUS_SantaRosaIndianCommunityOfTheSantaRosaRanc,
  /**
   * Santa Rosa Band of Cahuilla Mission Indians of the
   */
  SantaRosaBandOfCahuillaMissionIndiansOfThe: v3_TribalEntityUS_SantaRosaBandOfCahuillaMissionIndiansOfThe,
  /**
   * Santa Ynez Band of Chumash Mission Indians of the
   */
  SantaYnezBandOfChumashMissionIndiansOfThe: v3_TribalEntityUS_SantaYnezBandOfChumashMissionIndiansOfThe,
  /**
   * Santa Ysabel Band of Diegueno Mission Indians of t
   */
  SantaYsabelBandOfDieguenoMissionIndiansOfT: v3_TribalEntityUS_SantaYsabelBandOfDieguenoMissionIndiansOfT,
  /**
   * Cachil DeHe Band of Wintun Indians of the Colusa I
   */
  CachilDeHeBandOfWintunIndiansOfTheColusaI: v3_TribalEntityUS_CachilDeHeBandOfWintunIndiansOfTheColusaI,
  /**
   * Santee Sioux Tribe of the Santee Reservation of Ne
   */
  SanteeSiouxTribeOfTheSanteeReservationOfNe: v3_TribalEntityUS_SanteeSiouxTribeOfTheSanteeReservationOfNe,
  /**
   * Sauk-Suiattle Indian Tribe of Washington
   */
  SaukSuiattleIndianTribeOfWashington: v3_TribalEntityUS_SaukSuiattleIndianTribeOfWashington,
  /**
   * Sault Ste. Marie Tribe of Chippewa Indians of Mich
   */
  SaultSteMarieTribeOfChippewaIndiansOfMich: v3_TribalEntityUS_SaultSteMarieTribeOfChippewaIndiansOfMich,
  /**
   * Scotts Valley Band of Pomo Indians of California
   */
  ScottsValleyBandOfPomoIndiansOfCalifornia: v3_TribalEntityUS_ScottsValleyBandOfPomoIndiansOfCalifornia,
  /**
   * Seminole Nation of Oklahoma
   */
  SeminoleNationOfOklahoma: v3_TribalEntityUS_SeminoleNationOfOklahoma,
  /**
   * Seminole Tribe of Florida, Dania, Big Cypress, Bri
   */
  SeminoleTribeOfFloridaDaniaBigCypressBri: v3_TribalEntityUS_SeminoleTribeOfFloridaDaniaBigCypressBri,
  /**
   * Seneca Nation of New York
   */
  SenecaNationOfNewYork: v3_TribalEntityUS_SenecaNationOfNewYork,
  /**
   * Seneca-Cayuga Tribe of Oklahoma
   */
  SenecaCayugaTribeOfOklahoma: v3_TribalEntityUS_SenecaCayugaTribeOfOklahoma,
  /**
   * Shakopee Mdewakanton Sioux Community of Minnesota
   */
  ShakopeeMdewakantonSiouxCommunityOfMinnesota: v3_TribalEntityUS_ShakopeeMdewakantonSiouxCommunityOfMinnesota,
  /**
   * Shawnee Tribe, Oklahoma
   */
  ShawneeTribeOklahoma: v3_TribalEntityUS_ShawneeTribeOklahoma,
  /**
   * Caddo Indian Tribe of Oklahoma
   */
  CaddoIndianTribeOfOklahoma: v3_TribalEntityUS_CaddoIndianTribeOfOklahoma,
  /**
   * Sherwood Valley Rancheria of Pomo Indians of Calif
   */
  SherwoodValleyRancheriaOfPomoIndiansOfCalif: v3_TribalEntityUS_SherwoodValleyRancheriaOfPomoIndiansOfCalif,
  /**
   * Shingle Springs Band of Miwok Indians, Shingle Spr
   */
  ShingleSpringsBandOfMiwokIndiansShingleSpr: v3_TribalEntityUS_ShingleSpringsBandOfMiwokIndiansShingleSpr,
  /**
   * Shoalwater Bay Tribe of the Shoalwater Bay Indian
   */
  ShoalwaterBayTribeOfTheShoalwaterBayIndian: v3_TribalEntityUS_ShoalwaterBayTribeOfTheShoalwaterBayIndian,
  /**
   * Shoshone Tribe of the Wind River Reservation, Wyom
   */
  ShoshoneTribeOfTheWindRiverReservationWyom: v3_TribalEntityUS_ShoshoneTribeOfTheWindRiverReservationWyom,
  /**
   * Shoshone-Bannock Tribes of the Fort Hall Reservati
   */
  ShoshoneBannockTribesOfTheFortHallReservati: v3_TribalEntityUS_ShoshoneBannockTribesOfTheFortHallReservati,
  /**
   * Shoshone-Paiute Tribes of the Duck Valley Reservat
   */
  ShoshonePaiuteTribesOfTheDuckValleyReservat: v3_TribalEntityUS_ShoshonePaiuteTribesOfTheDuckValleyReservat,
  /**
   * Sisseton-Wahpeton Sioux Tribe of the Lake Traverse
   */
  SissetonWahpetonSiouxTribeOfTheLakeTraverse: v3_TribalEntityUS_SissetonWahpetonSiouxTribeOfTheLakeTraverse,
  /**
   * Skokomish Indian Tribe of the Skokomish Reservatio
   */
  SkokomishIndianTribeOfTheSkokomishReservatio: v3_TribalEntityUS_SkokomishIndianTribeOfTheSkokomishReservatio,
  /**
   * Skull Valley Band of Goshute Indians of Utah
   */
  SkullValleyBandOfGoshuteIndiansOfUtah: v3_TribalEntityUS_SkullValleyBandOfGoshuteIndiansOfUtah,
  /**
   * Smith River Rancheria, California
   */
  SmithRiverRancheriaCalifornia: v3_TribalEntityUS_SmithRiverRancheriaCalifornia,
  /**
   * Cahuilla Band of Mission Indians of the Cahuilla R
   */
  CahuillaBandOfMissionIndiansOfTheCahuillaR: v3_TribalEntityUS_CahuillaBandOfMissionIndiansOfTheCahuillaR,
  /**
   * Snoqualmie Tribe, Washington
   */
  SnoqualmieTribeWashington: v3_TribalEntityUS_SnoqualmieTribeWashington,
  /**
   * Soboba Band of Luiseno Indians, California (former
   */
  SobobaBandOfLuisenoIndiansCaliforniaFormer: v3_TribalEntityUS_SobobaBandOfLuisenoIndiansCaliforniaFormer,
  /**
   * Sokaogon Chippewa Community of the Mole Lake Band
   */
  SokaogonChippewaCommunityOfTheMoleLakeBand: v3_TribalEntityUS_SokaogonChippewaCommunityOfTheMoleLakeBand,
  /**
   * Southern Ute Indian Tribe of the Southern Ute Rese
   */
  SouthernUteIndianTribeOfTheSouthernUteRese: v3_TribalEntityUS_SouthernUteIndianTribeOfTheSouthernUteRese,
  /**
   * Spirit Lake Tribe, North Dakota (formerly known as
   */
  SpiritLakeTribeNorthDakotaFormerlyKnownAs: v3_TribalEntityUS_SpiritLakeTribeNorthDakotaFormerlyKnownAs,
  /**
   * Spokane Tribe of the Spokane Reservation, Washingt
   */
  SpokaneTribeOfTheSpokaneReservationWashingt: v3_TribalEntityUS_SpokaneTribeOfTheSpokaneReservationWashingt,
  /**
   * Squaxin Island Tribe of the Squaxin Island Reserva
   */
  SquaxinIslandTribeOfTheSquaxinIslandReserva: v3_TribalEntityUS_SquaxinIslandTribeOfTheSquaxinIslandReserva,
  /**
   * St. Croix Chippewa Indians of Wisconsin, St. Croix
   */
  StCroixChippewaIndiansOfWisconsinStCroix: v3_TribalEntityUS_StCroixChippewaIndiansOfWisconsinStCroix,
  /**
   * St. Regis Band of Mohawk Indians of New York
   */
  StRegisBandOfMohawkIndiansOfNewYork: v3_TribalEntityUS_StRegisBandOfMohawkIndiansOfNewYork,
  /**
   * Standing Rock Sioux Tribe of North & South Dakota
   */
  StandingRockSiouxTribeOfNorthAndSouthDakota: v3_TribalEntityUS_StandingRockSiouxTribeOfNorthAndSouthDakota,
  /**
   * Cahto Indian Tribe of the Laytonville Rancheria, C
   */
  CahtoIndianTribeOfTheLaytonvilleRancheriaC: v3_TribalEntityUS_CahtoIndianTribeOfTheLaytonvilleRancheriaC,
  /**
   * Stockbridge-Munsee Community of Mohican Indians of
   */
  StockbridgeMunseeCommunityOfMohicanIndiansOf: v3_TribalEntityUS_StockbridgeMunseeCommunityOfMohicanIndiansOf,
  /**
   * Stillaguamish Tribe of Washington
   */
  StillaguamishTribeOfWashington: v3_TribalEntityUS_StillaguamishTribeOfWashington,
  /**
   * Summit Lake Paiute Tribe of Nevada
   */
  SummitLakePaiuteTribeOfNevada: v3_TribalEntityUS_SummitLakePaiuteTribeOfNevada,
  /**
   * Suquamish Indian Tribe of the Port Madison Reserva
   */
  SuquamishIndianTribeOfThePortMadisonReserva: v3_TribalEntityUS_SuquamishIndianTribeOfThePortMadisonReserva,
  /**
   * Susanville Indian Rancheria, California
   */
  SusanvilleIndianRancheriaCalifornia: v3_TribalEntityUS_SusanvilleIndianRancheriaCalifornia,
  /**
   * Swinomish Indians of the Swinomish Reservation, Wa
   */
  SwinomishIndiansOfTheSwinomishReservationWa: v3_TribalEntityUS_SwinomishIndiansOfTheSwinomishReservationWa,
  /**
   * Sycuan Band of Diegueno Mission Indians of Califor
   */
  SycuanBandOfDieguenoMissionIndiansOfCalifor: v3_TribalEntityUS_SycuanBandOfDieguenoMissionIndiansOfCalifor,
  /**
   * Table Bluff Reservation - Wiyot Tribe, California
   */
  TableBluffReservationWiyotTribeCalifornia: v3_TribalEntityUS_TableBluffReservationWiyotTribeCalifornia,
  /**
   * Table Mountain Rancheria of California
   */
  TableMountainRancheriaOfCalifornia: v3_TribalEntityUS_TableMountainRancheriaOfCalifornia,
  /**
   * Te-Moak Tribe of Western Shoshone Indians of Nevad
   */
  TeMoakTribeOfWesternShoshoneIndiansOfNevad: v3_TribalEntityUS_TeMoakTribeOfWesternShoshoneIndiansOfNevad,
  /**
   * Ak Chin Indian Community of the Maricopa (Ak Chin)
   */
  AkChinIndianCommunityOfTheMaricopaAkChin: v3_TribalEntityUS_AkChinIndianCommunityOfTheMaricopaAkChin,
  /**
   * California Valley Miwok Tribe, California (formerl
   */
  CaliforniaValleyMiwokTribeCaliforniaFormerl: v3_TribalEntityUS_CaliforniaValleyMiwokTribeCaliforniaFormerl,
  /**
   * Thlopthlocco Tribal Town, Oklahoma
   */
  ThlopthloccoTribalTownOklahoma: v3_TribalEntityUS_ThlopthloccoTribalTownOklahoma,
  /**
   * Three Affiliated Tribes of the Fort Berthold Reser
   */
  ThreeAffiliatedTribesOfTheFortBertholdReser: v3_TribalEntityUS_ThreeAffiliatedTribesOfTheFortBertholdReser,
  /**
   * Tohono O'odham Nation of Arizona
   */
  TohonoOQuoteOdhamNationOfArizona: v3_TribalEntityUS_TohonoOQuoteOdhamNationOfArizona,
  /**
   * Tonawanda Band of Seneca Indians of New York
   */
  TonawandaBandOfSenecaIndiansOfNewYork: v3_TribalEntityUS_TonawandaBandOfSenecaIndiansOfNewYork,
  /**
   * Tonkawa Tribe of Indians of Oklahoma
   */
  TonkawaTribeOfIndiansOfOklahoma: v3_TribalEntityUS_TonkawaTribeOfIndiansOfOklahoma,
  /**
   * Tonto Apache Tribe of Arizona
   */
  TontoApacheTribeOfArizona: v3_TribalEntityUS_TontoApacheTribeOfArizona,
  /**
   * Torres-Martinez Band of Cahuilla Mission Indians o
   */
  TorresMartinezBandOfCahuillaMissionIndiansO: v3_TribalEntityUS_TorresMartinezBandOfCahuillaMissionIndiansO,
  /**
   * Tule River Indian Tribe of the Tule River Reservat
   */
  TuleRiverIndianTribeOfTheTuleRiverReservat: v3_TribalEntityUS_TuleRiverIndianTribeOfTheTuleRiverReservat,
  /**
   * Tulalip Tribes of the Tulalip Reservation, Washing
   */
  TulalipTribesOfTheTulalipReservationWashing: v3_TribalEntityUS_TulalipTribesOfTheTulalipReservationWashing,
  /**
   * Tunica-Biloxi Indian Tribe of Louisiana
   */
  TunicaBiloxiIndianTribeOfLouisiana: v3_TribalEntityUS_TunicaBiloxiIndianTribeOfLouisiana,
  /**
   * Campo Band of Diegueno Mission Indians of the Camp
   */
  CampoBandOfDieguenoMissionIndiansOfTheCamp: v3_TribalEntityUS_CampoBandOfDieguenoMissionIndiansOfTheCamp,
  /**
   * Tuolumne Band of Me-Wuk Indians of the Tuolumne Ra
   */
  TuolumneBandOfMeWukIndiansOfTheTuolumneRa: v3_TribalEntityUS_TuolumneBandOfMeWukIndiansOfTheTuolumneRa,
  /**
   * Turtle Mountain Band of Chippewa Indians of North
   */
  TurtleMountainBandOfChippewaIndiansOfNorth: v3_TribalEntityUS_TurtleMountainBandOfChippewaIndiansOfNorth,
  /**
   * Tuscarora Nation of New York
   */
  TuscaroraNationOfNewYork: v3_TribalEntityUS_TuscaroraNationOfNewYork,
  /**
   * Twenty-Nine Palms Band of Mission Indians of Calif
   */
  TwentyNinePalmsBandOfMissionIndiansOfCalif: v3_TribalEntityUS_TwentyNinePalmsBandOfMissionIndiansOfCalif,
  /**
   * United Auburn Indian Community of the Auburn Ranch
   */
  UnitedAuburnIndianCommunityOfTheAuburnRanch: v3_TribalEntityUS_UnitedAuburnIndianCommunityOfTheAuburnRanch,
  /**
   * United Keetoowah Band of Cherokee Indians of Oklah
   */
  UnitedKeetoowahBandOfCherokeeIndiansOfOklah: v3_TribalEntityUS_UnitedKeetoowahBandOfCherokeeIndiansOfOklah,
  /**
   * Upper Lake Band of Pomo Indians of Upper Lake Ranc
   */
  UpperLakeBandOfPomoIndiansOfUpperLakeRanc: v3_TribalEntityUS_UpperLakeBandOfPomoIndiansOfUpperLakeRanc,
  /**
   * Upper Sioux Indian Community of the Upper Sioux Re
   */
  UpperSiouxIndianCommunityOfTheUpperSiouxRe: v3_TribalEntityUS_UpperSiouxIndianCommunityOfTheUpperSiouxRe,
  /**
   * Upper Skagit Indian Tribe of Washington
   */
  UpperSkagitIndianTribeOfWashington: v3_TribalEntityUS_UpperSkagitIndianTribeOfWashington,
  /**
   * Ute Indian Tribe of the Uintah & Ouray Reservation
   */
  UteIndianTribeOfTheUintahAndOurayReservation: v3_TribalEntityUS_UteIndianTribeOfTheUintahAndOurayReservation,
  /**
   * Capitan Grande Band of Diegueno Mission Indians of
   */
  CapitanGrandeBandOfDieguenoMissionIndiansOf: v3_TribalEntityUS_CapitanGrandeBandOfDieguenoMissionIndiansOf,
  /**
   * Ute Mountain Tribe of the Ute Mountain Reservation
   */
  UteMountainTribeOfTheUteMountainReservation: v3_TribalEntityUS_UteMountainTribeOfTheUteMountainReservation,
  /**
   * Utu Utu Gwaitu Paiute Tribe of the Benton Paiute R
   */
  UtuUtuGwaituPaiuteTribeOfTheBentonPaiuteR: v3_TribalEntityUS_UtuUtuGwaituPaiuteTribeOfTheBentonPaiuteR,
  /**
   * Walker River Paiute Tribe of the Walker River Rese
   */
  WalkerRiverPaiuteTribeOfTheWalkerRiverRese: v3_TribalEntityUS_WalkerRiverPaiuteTribeOfTheWalkerRiverRese,
  /**
   * Wampanoag Tribe of Gay Head (Aquinnah) of Massachu
   */
  WampanoagTribeOfGayHeadAquinnahOfMassachu: v3_TribalEntityUS_WampanoagTribeOfGayHeadAquinnahOfMassachu,
  /**
   * Washoe Tribe of Nevada & California (Carson Colony
   */
  WashoeTribeOfNevadaAndCaliforniaCarsonColony: v3_TribalEntityUS_WashoeTribeOfNevadaAndCaliforniaCarsonColony,
  /**
   * White Mountain Apache Tribe of the Fort Apache Res
   */
  WhiteMountainApacheTribeOfTheFortApacheRes: v3_TribalEntityUS_WhiteMountainApacheTribeOfTheFortApacheRes,
  /**
   * Wichita and Affiliated Tribes (Wichita, Keechi, Wa
   */
  WichitaAndAffiliatedTribesWichitaKeechiWa: v3_TribalEntityUS_WichitaAndAffiliatedTribesWichitaKeechiWa,
  /**
   * Winnebago Tribe of Nebraska
   */
  WinnebagoTribeOfNebraska: v3_TribalEntityUS_WinnebagoTribeOfNebraska,
  /**
   * Winnemucca Indian Colony of Nevada
   */
  WinnemuccaIndianColonyOfNevada: v3_TribalEntityUS_WinnemuccaIndianColonyOfNevada,
  /**
   * Wyandotte Tribe of Oklahoma
   */
  WyandotteTribeOfOklahoma: v3_TribalEntityUS_WyandotteTribeOfOklahoma,
  /**
   * Barona Group of Capitan Grande Band of Mission Ind
   */
  BaronaGroupOfCapitanGrandeBandOfMissionInd: v3_TribalEntityUS_BaronaGroupOfCapitanGrandeBandOfMissionInd,
  /**
   * Yankton Sioux Tribe of South Dakota
   */
  YanktonSiouxTribeOfSouthDakota: v3_TribalEntityUS_YanktonSiouxTribeOfSouthDakota,
  /**
   * Yavapai-Apache Nation of the Camp Verde Indian Res
   */
  YavapaiApacheNationOfTheCampVerdeIndianRes: v3_TribalEntityUS_YavapaiApacheNationOfTheCampVerdeIndianRes,
  /**
   * Yavapai-Prescott Tribe of the Yavapai Reservation,
   */
  YavapaiPrescottTribeOfTheYavapaiReservation: v3_TribalEntityUS_YavapaiPrescottTribeOfTheYavapaiReservation,
  /**
   * Yerington Paiute Tribe of the Yerington Colony & C
   */
  YeringtonPaiuteTribeOfTheYeringtonColonyAndC: v3_TribalEntityUS_YeringtonPaiuteTribeOfTheYeringtonColonyAndC,
  /**
   * Yomba Shoshone Tribe of the Yomba Reservation, Nev
   */
  YombaShoshoneTribeOfTheYombaReservationNev: v3_TribalEntityUS_YombaShoshoneTribeOfTheYombaReservationNev,
  /**
   * Ysleta Del Sur Pueblo of Texas
   */
  YsletaDelSurPuebloOfTexas: v3_TribalEntityUS_YsletaDelSurPuebloOfTexas,
  /**
   * Yurok Tribe of the Yurok Reservation, California
   */
  YurokTribeOfTheYurokReservationCalifornia: v3_TribalEntityUS_YurokTribeOfTheYurokReservationCalifornia,
  /**
   * Zuni Tribe of the Zuni Reservation, New Mexico
   */
  ZuniTribeOfTheZuniReservationNewMexico: v3_TribalEntityUS_ZuniTribeOfTheZuniReservationNewMexico,
  /**
   * Village of Afognak
   */
  VillageOfAfognak: v3_TribalEntityUS_VillageOfAfognak,
  /**
   * Agdaagux Tribe of King Cove
   */
  AgdaaguxTribeOfKingCove: v3_TribalEntityUS_AgdaaguxTribeOfKingCove,
  /**
   * Viejas (Baron Long) Group of Capitan Grande Band o
   */
  ViejasBaronLongGroupOfCapitanGrandeBandO: v3_TribalEntityUS_ViejasBaronLongGroupOfCapitanGrandeBandO,
  /**
   * Native Village of Akhiok
   */
  NativeVillageOfAkhiok: v3_TribalEntityUS_NativeVillageOfAkhiok,
  /**
   * Akiachak Native Community
   */
  AkiachakNativeCommunity: v3_TribalEntityUS_AkiachakNativeCommunity,
  /**
   * Akiak Native Community
   */
  AkiakNativeCommunity: v3_TribalEntityUS_AkiakNativeCommunity,
  /**
   * Native Village of Akutan
   */
  NativeVillageOfAkutan: v3_TribalEntityUS_NativeVillageOfAkutan,
  /**
   * Village of Alakanuk
   */
  VillageOfAlakanuk: v3_TribalEntityUS_VillageOfAlakanuk,
  /**
   * Alatna Village
   */
  AlatnaVillage: v3_TribalEntityUS_AlatnaVillage,
  /**
   * Native Village of Aleknagik
   */
  NativeVillageOfAleknagik: v3_TribalEntityUS_NativeVillageOfAleknagik,
  /**
   * Algaaciq Native Village (St. Mary's)
   */
  AlgaaciqNativeVillageStMaryQuoteS: v3_TribalEntityUS_AlgaaciqNativeVillageStMaryQuoteS,
  /**
   * Allakaket Village
   */
  AllakaketVillage: v3_TribalEntityUS_AllakaketVillage,
  /**
   * Native Village of Ambler
   */
  NativeVillageOfAmbler: v3_TribalEntityUS_NativeVillageOfAmbler,
  /**
   * Catawba Indian Nation (aka Catawba Tribe of South
   */
  CatawbaIndianNationAkaCatawbaTribeOfSouth: v3_TribalEntityUS_CatawbaIndianNationAkaCatawbaTribeOfSouth,
  /**
   * Village of Anaktuvuk Pass
   */
  VillageOfAnaktuvukPass: v3_TribalEntityUS_VillageOfAnaktuvukPass,
  /**
   * Yupiit of Andreafski
   */
  YupiitOfAndreafski: v3_TribalEntityUS_YupiitOfAndreafski,
  /**
   * Angoon Community Association
   */
  AngoonCommunityAssociation: v3_TribalEntityUS_AngoonCommunityAssociation,
  /**
   * Village of Aniak
   */
  VillageOfAniak: v3_TribalEntityUS_VillageOfAniak,
  /**
   * Anvik Village
   */
  AnvikVillage: v3_TribalEntityUS_AnvikVillage,
  /**
   * Arctic Village (See Native Village of Venetie Trib
   */
  ArcticVillageSeeNativeVillageOfVenetieTrib: v3_TribalEntityUS_ArcticVillageSeeNativeVillageOfVenetieTrib,
  /**
   * Asa carsarmiut Tribe (formerly Native Village of M
   */
  AsaCarsarmiutTribeFormerlyNativeVillageOfM: v3_TribalEntityUS_AsaCarsarmiutTribeFormerlyNativeVillageOfM,
  /**
   * Native Village of Atka
   */
  NativeVillageOfAtka: v3_TribalEntityUS_NativeVillageOfAtka,
  /**
   * Village of Atmautluak
   */
  VillageOfAtmautluak: v3_TribalEntityUS_VillageOfAtmautluak,
  /**
   * Atqasuk Village (Atkasook)
   */
  AtqasukVillageAtkasook: v3_TribalEntityUS_AtqasukVillageAtkasook,
  /**
   * Cayuga Nation of New York
   */
  CayugaNationOfNewYork: v3_TribalEntityUS_CayugaNationOfNewYork,
  /**
   * Native Village of Barrow Inupiat Traditional Gover
   */
  NativeVillageOfBarrowInupiatTraditionalGover: v3_TribalEntityUS_NativeVillageOfBarrowInupiatTraditionalGover,
  /**
   * Beaver Village
   */
  BeaverVillage: v3_TribalEntityUS_BeaverVillage,
  /**
   * Native Village of Belkofski
   */
  NativeVillageOfBelkofski: v3_TribalEntityUS_NativeVillageOfBelkofski,
  /**
   * Village of Bill Moore's Slough
   */
  VillageOfBillMooreQuoteSSlough: v3_TribalEntityUS_VillageOfBillMooreQuoteSSlough,
  /**
   * Birch Creek Tribe
   */
  BirchCreekTribe: v3_TribalEntityUS_BirchCreekTribe,
  /**
   * Native Village of Brevig Mission
   */
  NativeVillageOfBrevigMission: v3_TribalEntityUS_NativeVillageOfBrevigMission,
  /**
   * Native Village of Buckland
   */
  NativeVillageOfBuckland: v3_TribalEntityUS_NativeVillageOfBuckland,
  /**
   * Native Village of Cantwell
   */
  NativeVillageOfCantwell: v3_TribalEntityUS_NativeVillageOfCantwell,
  /**
   * Native Village of Chanega (aka Chenega)
   */
  NativeVillageOfChanegaAkaChenega: v3_TribalEntityUS_NativeVillageOfChanegaAkaChenega,
  /**
   * Chalkyitsik Village
   */
  ChalkyitsikVillage: v3_TribalEntityUS_ChalkyitsikVillage,
  /**
   * Cedarville Rancheria, California
   */
  CedarvilleRancheriaCalifornia: v3_TribalEntityUS_CedarvilleRancheriaCalifornia,
  /**
   * Village of Chefornak
   */
  VillageOfChefornak: v3_TribalEntityUS_VillageOfChefornak,
  /**
   * Chevak Native Village
   */
  ChevakNativeVillage: v3_TribalEntityUS_ChevakNativeVillage,
  /**
   * Chickaloon Native Village
   */
  ChickaloonNativeVillage: v3_TribalEntityUS_ChickaloonNativeVillage,
  /**
   * Native Village of Chignik
   */
  NativeVillageOfChignik: v3_TribalEntityUS_NativeVillageOfChignik,
  /**
   * Native Village of Chignik Lagoon
   */
  NativeVillageOfChignikLagoon: v3_TribalEntityUS_NativeVillageOfChignikLagoon,
  /**
   * Chignik Lake Village
   */
  ChignikLakeVillage: v3_TribalEntityUS_ChignikLakeVillage,
  /**
   * Chilkat Indian Village (Klukwan)
   */
  ChilkatIndianVillageKlukwan: v3_TribalEntityUS_ChilkatIndianVillageKlukwan,
  /**
   * Chilkoot Indian Association (Haines)
   */
  ChilkootIndianAssociationHaines: v3_TribalEntityUS_ChilkootIndianAssociationHaines,
  /**
   * Chinik Eskimo Community (Golovin)
   */
  ChinikEskimoCommunityGolovin: v3_TribalEntityUS_ChinikEskimoCommunityGolovin,
  /**
   * Native Village of Chistochina
   */
  NativeVillageOfChistochina: v3_TribalEntityUS_NativeVillageOfChistochina,
  /**
   * Chemehuevi Indian Tribe of the Chemehuevi Reservat
   */
  ChemehueviIndianTribeOfTheChemehueviReservat: v3_TribalEntityUS_ChemehueviIndianTribeOfTheChemehueviReservat,
  /**
   * Native Village of Chitina
   */
  NativeVillageOfChitina: v3_TribalEntityUS_NativeVillageOfChitina,
  /**
   * Native Village of Chuathbaluk (Russian Mission, Ku
   */
  NativeVillageOfChuathbalukRussianMissionKu: v3_TribalEntityUS_NativeVillageOfChuathbalukRussianMissionKu,
  /**
   * Chuloonawick Native Village
   */
  ChuloonawickNativeVillage: v3_TribalEntityUS_ChuloonawickNativeVillage,
  /**
   * Circle Native Community
   */
  CircleNativeCommunity: v3_TribalEntityUS_CircleNativeCommunity,
  /**
   * Village of Clark's Point
   */
  VillageOfClarkQuoteSPoint: v3_TribalEntityUS_VillageOfClarkQuoteSPoint,
  /**
   * Native Village of Council
   */
  NativeVillageOfCouncil: v3_TribalEntityUS_NativeVillageOfCouncil,
  /**
   * Craig Community Association
   */
  CraigCommunityAssociation: v3_TribalEntityUS_CraigCommunityAssociation,
  /**
   * Village of Crooked Creek
   */
  VillageOfCrookedCreek: v3_TribalEntityUS_VillageOfCrookedCreek,
  /**
   * Curyung Tribal Council (formerly Native Village of
   */
  CuryungTribalCouncilFormerlyNativeVillageOf: v3_TribalEntityUS_CuryungTribalCouncilFormerlyNativeVillageOf,
  /**
   * Native Village of Deering
   */
  NativeVillageOfDeering: v3_TribalEntityUS_NativeVillageOfDeering,
  /**
   * Cher-Ae Heights Indian Community of the Trinidad R
   */
  CherAeHeightsIndianCommunityOfTheTrinidadR: v3_TribalEntityUS_CherAeHeightsIndianCommunityOfTheTrinidadR,
  /**
   * Native Village of Diomede (aka Inalik)
   */
  NativeVillageOfDiomedeAkaInalik: v3_TribalEntityUS_NativeVillageOfDiomedeAkaInalik,
  /**
   * Village of Dot Lake
   */
  VillageOfDotLake: v3_TribalEntityUS_VillageOfDotLake,
  /**
   * Douglas Indian Association
   */
  DouglasIndianAssociation: v3_TribalEntityUS_DouglasIndianAssociation,
  /**
   * Native Village of Eagle
   */
  NativeVillageOfEagle: v3_TribalEntityUS_NativeVillageOfEagle,
  /**
   * Native Village of Eek
   */
  NativeVillageOfEek: v3_TribalEntityUS_NativeVillageOfEek,
  /**
   * Egegik Village
   */
  EgegikVillage: v3_TribalEntityUS_EgegikVillage,
  /**
   * Eklutna Native Village
   */
  EklutnaNativeVillage: v3_TribalEntityUS_EklutnaNativeVillage,
  /**
   * Native Village of Ekuk
   */
  NativeVillageOfEkuk: v3_TribalEntityUS_NativeVillageOfEkuk,
  /**
   * Ekwok Village
   */
  EkwokVillage: v3_TribalEntityUS_EkwokVillage,
  /**
   * Native Village of Elim
   */
  NativeVillageOfElim: v3_TribalEntityUS_NativeVillageOfElim,
  /**
   * Alabama-Coushatta Tribes of Texas
   */
  AlabamaCoushattaTribesOfTexas: v3_TribalEntityUS_AlabamaCoushattaTribesOfTexas,
  /**
   * Cherokee Nation, Oklahoma
   */
  CherokeeNationOklahoma: v3_TribalEntityUS_CherokeeNationOklahoma,
  /**
   * Emmonak Village
   */
  EmmonakVillage: v3_TribalEntityUS_EmmonakVillage,
  /**
   * Evansville Village (aka Bettles Field)
   */
  EvansvilleVillageAkaBettlesField: v3_TribalEntityUS_EvansvilleVillageAkaBettlesField,
  /**
   * Native Village of Eyak (Cordova)
   */
  NativeVillageOfEyakCordova: v3_TribalEntityUS_NativeVillageOfEyakCordova,
  /**
   * Native Village of False Pass
   */
  NativeVillageOfFalsePass: v3_TribalEntityUS_NativeVillageOfFalsePass,
  /**
   * Native Village of Fort Yukon
   */
  NativeVillageOfFortYukon: v3_TribalEntityUS_NativeVillageOfFortYukon,
  /**
   * Native Village of Gakona
   */
  NativeVillageOfGakona: v3_TribalEntityUS_NativeVillageOfGakona,
  /**
   * Galena Village (aka Louden Village)
   */
  GalenaVillageAkaLoudenVillage: v3_TribalEntityUS_GalenaVillageAkaLoudenVillage,
  /**
   * Native Village of Gambell
   */
  NativeVillageOfGambell: v3_TribalEntityUS_NativeVillageOfGambell,
  /**
   * Native Village of Georgetown
   */
  NativeVillageOfGeorgetown: v3_TribalEntityUS_NativeVillageOfGeorgetown,
  /**
   * Native Village of Goodnews Bay
   */
  NativeVillageOfGoodnewsBay: v3_TribalEntityUS_NativeVillageOfGoodnewsBay,
  /**
   * Cheyenne-Arapaho Tribes of Oklahoma
   */
  CheyenneArapahoTribesOfOklahoma: v3_TribalEntityUS_CheyenneArapahoTribesOfOklahoma,
  /**
   * Organized Village of Grayling (aka Holikachuk)
   */
  OrganizedVillageOfGraylingAkaHolikachuk: v3_TribalEntityUS_OrganizedVillageOfGraylingAkaHolikachuk,
  /**
   * Gulkana Village
   */
  GulkanaVillage: v3_TribalEntityUS_GulkanaVillage,
  /**
   * Native Village of Hamilton
   */
  NativeVillageOfHamilton: v3_TribalEntityUS_NativeVillageOfHamilton,
  /**
   * Healy Lake Village
   */
  HealyLakeVillage: v3_TribalEntityUS_HealyLakeVillage,
  /**
   * Holy Cross Village
   */
  HolyCrossVillage: v3_TribalEntityUS_HolyCrossVillage,
  /**
   * Hoonah Indian Association
   */
  HoonahIndianAssociation: v3_TribalEntityUS_HoonahIndianAssociation,
  /**
   * Native Village of Hooper Bay
   */
  NativeVillageOfHooperBay: v3_TribalEntityUS_NativeVillageOfHooperBay,
  /**
   * Hughes Village
   */
  HughesVillage: v3_TribalEntityUS_HughesVillage,
  /**
   * Huslia Village
   */
  HusliaVillage: v3_TribalEntityUS_HusliaVillage,
  /**
   * Hydaburg Cooperative Association
   */
  HydaburgCooperativeAssociation: v3_TribalEntityUS_HydaburgCooperativeAssociation,
  /**
   * Cheyenne River Sioux Tribe of the Cheyenne River R
   */
  CheyenneRiverSiouxTribeOfTheCheyenneRiverR: v3_TribalEntityUS_CheyenneRiverSiouxTribeOfTheCheyenneRiverR,
  /**
   * Igiugig Village
   */
  IgiugigVillage: v3_TribalEntityUS_IgiugigVillage,
  /**
   * Village of Iliamna
   */
  VillageOfIliamna: v3_TribalEntityUS_VillageOfIliamna,
  /**
   * Inupiat Community of the Arctic Slope
   */
  InupiatCommunityOfTheArcticSlope: v3_TribalEntityUS_InupiatCommunityOfTheArcticSlope,
  /**
   * Iqurmuit Traditional Council (formerly Native Vill
   */
  IqurmuitTraditionalCouncilFormerlyNativeVill: v3_TribalEntityUS_IqurmuitTraditionalCouncilFormerlyNativeVill,
  /**
   * Ivanoff Bay Village
   */
  IvanoffBayVillage: v3_TribalEntityUS_IvanoffBayVillage,
  /**
   * Kaguyak Village
   */
  KaguyakVillage: v3_TribalEntityUS_KaguyakVillage,
  /**
   * Organized Village of Kake
   */
  OrganizedVillageOfKake: v3_TribalEntityUS_OrganizedVillageOfKake,
  /**
   * Kaktovik Village (aka Barter Island)
   */
  KaktovikVillageAkaBarterIsland: v3_TribalEntityUS_KaktovikVillageAkaBarterIsland,
  /**
   * Village of Kalskag
   */
  VillageOfKalskag: v3_TribalEntityUS_VillageOfKalskag,
  /**
   * Village of Kaltag
   */
  VillageOfKaltag: v3_TribalEntityUS_VillageOfKaltag,
  /**
   * Chickasaw Nation, Oklahoma
   */
  ChickasawNationOklahoma: v3_TribalEntityUS_ChickasawNationOklahoma,
  /**
   * Native Village of Kanatak
   */
  NativeVillageOfKanatak: v3_TribalEntityUS_NativeVillageOfKanatak,
  /**
   * Native Village of Karluk
   */
  NativeVillageOfKarluk: v3_TribalEntityUS_NativeVillageOfKarluk,
  /**
   * Organized Village of Kasaan
   */
  OrganizedVillageOfKasaan: v3_TribalEntityUS_OrganizedVillageOfKasaan,
  /**
   * Native Village of Kasigluk
   */
  NativeVillageOfKasigluk: v3_TribalEntityUS_NativeVillageOfKasigluk,
  /**
   * Kenaitze Indian Tribe
   */
  KenaitzeIndianTribe: v3_TribalEntityUS_KenaitzeIndianTribe,
  /**
   * Ketchikan Indian Corporation
   */
  KetchikanIndianCorporation: v3_TribalEntityUS_KetchikanIndianCorporation,
  /**
   * Native Village of Kiana
   */
  NativeVillageOfKiana: v3_TribalEntityUS_NativeVillageOfKiana,
  /**
   * King Island Native Community
   */
  KingIslandNativeCommunity: v3_TribalEntityUS_KingIslandNativeCommunity,
  /**
   * King Salmon Tribe
   */
  KingSalmonTribe: v3_TribalEntityUS_KingSalmonTribe,
  /**
   * Native Village of Kipnuk
   */
  NativeVillageOfKipnuk: v3_TribalEntityUS_NativeVillageOfKipnuk,
  /**
   * Chicken Ranch Rancheria of Me-Wuk Indians of Calif
   */
  ChickenRanchRancheriaOfMeWukIndiansOfCalif: v3_TribalEntityUS_ChickenRanchRancheriaOfMeWukIndiansOfCalif,
  /**
   * Native Village of Kivalina
   */
  NativeVillageOfKivalina: v3_TribalEntityUS_NativeVillageOfKivalina,
  /**
   * Klawock Cooperative Association
   */
  KlawockCooperativeAssociation: v3_TribalEntityUS_KlawockCooperativeAssociation,
  /**
   * Native Village of Kluti Kaah (aka Copper Center)
   */
  NativeVillageOfKlutiKaahAkaCopperCenter: v3_TribalEntityUS_NativeVillageOfKlutiKaahAkaCopperCenter,
  /**
   * Knik Tribe
   */
  KnikTribe: v3_TribalEntityUS_KnikTribe,
  /**
   * Native Village of Kobuk
   */
  NativeVillageOfKobuk: v3_TribalEntityUS_NativeVillageOfKobuk,
  /**
   * Kokhanok Village
   */
  KokhanokVillage: v3_TribalEntityUS_KokhanokVillage,
  /**
   * Native Village of Kongiganak
   */
  NativeVillageOfKongiganak: v3_TribalEntityUS_NativeVillageOfKongiganak,
  /**
   * Village of Kotlik
   */
  VillageOfKotlik: v3_TribalEntityUS_VillageOfKotlik,
  /**
   * Native Village of Kotzebue
   */
  NativeVillageOfKotzebue: v3_TribalEntityUS_NativeVillageOfKotzebue,
  /**
   * Native Village of Koyuk
   */
  NativeVillageOfKoyuk: v3_TribalEntityUS_NativeVillageOfKoyuk,
  /**
   * Chippewa-Cree Indians of the Rocky Boy's Reservati
   */
  ChippewaCreeIndiansOfTheRockyBoyQuoteSReservati: v3_TribalEntityUS_ChippewaCreeIndiansOfTheRockyBoyQuoteSReservati,
  /**
   * Koyukuk Native Village
   */
  KoyukukNativeVillage: v3_TribalEntityUS_KoyukukNativeVillage,
  /**
   * Organized Village of Kwethluk
   */
  OrganizedVillageOfKwethluk: v3_TribalEntityUS_OrganizedVillageOfKwethluk,
  /**
   * Native Village of Kwigillingok
   */
  NativeVillageOfKwigillingok: v3_TribalEntityUS_NativeVillageOfKwigillingok,
  /**
   * Native Village of Kwinhagak (aka Quinhagak)
   */
  NativeVillageOfKwinhagakAkaQuinhagak: v3_TribalEntityUS_NativeVillageOfKwinhagakAkaQuinhagak,
  /**
   * Native Village of Larsen Bay
   */
  NativeVillageOfLarsenBay: v3_TribalEntityUS_NativeVillageOfLarsenBay,
  /**
   * Levelock Village
   */
  LevelockVillage: v3_TribalEntityUS_LevelockVillage,
  /**
   * Lesnoi Village (aka Woody Island)
   */
  LesnoiVillageAkaWoodyIsland: v3_TribalEntityUS_LesnoiVillageAkaWoodyIsland,
  /**
   * Lime Village
   */
  LimeVillage: v3_TribalEntityUS_LimeVillage,
  /**
   * Village of Lower Kalskag
   */
  VillageOfLowerKalskag: v3_TribalEntityUS_VillageOfLowerKalskag,
  /**
   * Manley Hot Springs Village
   */
  ManleyHotSpringsVillage: v3_TribalEntityUS_ManleyHotSpringsVillage,
  /**
   * Chitimacha Tribe of Louisiana
   */
  ChitimachaTribeOfLouisiana: v3_TribalEntityUS_ChitimachaTribeOfLouisiana,
  /**
   * Manokotak Village
   */
  ManokotakVillage: v3_TribalEntityUS_ManokotakVillage,
  /**
   * Native Village of Marshall (aka Fortuna Ledge)
   */
  NativeVillageOfMarshallAkaFortunaLedge: v3_TribalEntityUS_NativeVillageOfMarshallAkaFortunaLedge,
  /**
   * Native Village of Mary's Igloo
   */
  NativeVillageOfMaryQuoteSIgloo: v3_TribalEntityUS_NativeVillageOfMaryQuoteSIgloo,
  /**
   * McGrath Native Village
   */
  McGrathNativeVillage: v3_TribalEntityUS_McGrathNativeVillage,
  /**
   * Native Village of Mekoryuk
   */
  NativeVillageOfMekoryuk: v3_TribalEntityUS_NativeVillageOfMekoryuk,
  /**
   * Mentasta Traditional Council
   */
  MentastaTraditionalCouncil: v3_TribalEntityUS_MentastaTraditionalCouncil,
  /**
   * Metlakatla Indian Community, Annette Island Reserv
   */
  MetlakatlaIndianCommunityAnnetteIslandReserv: v3_TribalEntityUS_MetlakatlaIndianCommunityAnnetteIslandReserv,
  /**
   * Native Village of Minto
   */
  NativeVillageOfMinto: v3_TribalEntityUS_NativeVillageOfMinto,
  /**
   * Naknek Native Village
   */
  NaknekNativeVillage: v3_TribalEntityUS_NaknekNativeVillage,
  /**
   * Native Village of Nanwalek (aka English Bay)
   */
  NativeVillageOfNanwalekAkaEnglishBay: v3_TribalEntityUS_NativeVillageOfNanwalekAkaEnglishBay,
  /**
   * Choctaw Nation of Oklahoma
   */
  ChoctawNationOfOklahoma: v3_TribalEntityUS_ChoctawNationOfOklahoma,
  /**
   * Native Village of Napaimute
   */
  NativeVillageOfNapaimute: v3_TribalEntityUS_NativeVillageOfNapaimute,
  /**
   * Native Village of Napakiak
   */
  NativeVillageOfNapakiak: v3_TribalEntityUS_NativeVillageOfNapakiak,
  /**
   * Native Village of Napaskiak
   */
  NativeVillageOfNapaskiak: v3_TribalEntityUS_NativeVillageOfNapaskiak,
  /**
   * Native Village of Nelson Lagoon
   */
  NativeVillageOfNelsonLagoon: v3_TribalEntityUS_NativeVillageOfNelsonLagoon,
  /**
   * Nenana Native Association
   */
  NenanaNativeAssociation: v3_TribalEntityUS_NenanaNativeAssociation,
  /**
   * New Koliganek Village Council (formerly Koliganek
   */
  NewKoliganekVillageCouncilFormerlyKoliganek: v3_TribalEntityUS_NewKoliganekVillageCouncilFormerlyKoliganek,
  /**
   * New Stuyahok Village
   */
  NewStuyahokVillage: v3_TribalEntityUS_NewStuyahokVillage,
  /**
   * Newhalen Village
   */
  NewhalenVillage: v3_TribalEntityUS_NewhalenVillage,
  /**
   * Newtok Village
   */
  NewtokVillage: v3_TribalEntityUS_NewtokVillage,
  /**
   * Native Village of Nightmute
   */
  NativeVillageOfNightmute: v3_TribalEntityUS_NativeVillageOfNightmute,
  /**
   * Citizen Potawatomi Nation, Oklahoma
   */
  CitizenPotawatomiNationOklahoma: v3_TribalEntityUS_CitizenPotawatomiNationOklahoma,
  /**
   * Nikolai Village
   */
  NikolaiVillage: v3_TribalEntityUS_NikolaiVillage,
  /**
   * Native Village of Nikolski
   */
  NativeVillageOfNikolski: v3_TribalEntityUS_NativeVillageOfNikolski,
  /**
   * Ninilchik Village
   */
  NinilchikVillage: v3_TribalEntityUS_NinilchikVillage,
  /**
   * Native Village of Noatak
   */
  NativeVillageOfNoatak: v3_TribalEntityUS_NativeVillageOfNoatak,
  /**
   * Nome Eskimo Community
   */
  NomeEskimoCommunity: v3_TribalEntityUS_NomeEskimoCommunity,
  /**
   * Nondalton Village
   */
  NondaltonVillage: v3_TribalEntityUS_NondaltonVillage,
  /**
   * Noorvik Native Community
   */
  NoorvikNativeCommunity: v3_TribalEntityUS_NoorvikNativeCommunity,
  /**
   * Northway Village
   */
  NorthwayVillage: v3_TribalEntityUS_NorthwayVillage,
  /**
   * Native Village of Nuiqsut (aka Nooiksut)
   */
  NativeVillageOfNuiqsutAkaNooiksut: v3_TribalEntityUS_NativeVillageOfNuiqsutAkaNooiksut,
  /**
   * Nulato Village
   */
  NulatoVillage: v3_TribalEntityUS_NulatoVillage,
  /**
   * Cloverdale Rancheria of Pomo Indians of California
   */
  CloverdaleRancheriaOfPomoIndiansOfCalifornia: v3_TribalEntityUS_CloverdaleRancheriaOfPomoIndiansOfCalifornia,
  /**
   * Nunakauyarmiut Tribe (formerly Native Village of T
   */
  NunakauyarmiutTribeFormerlyNativeVillageOfT: v3_TribalEntityUS_NunakauyarmiutTribeFormerlyNativeVillageOfT,
  /**
   * Native Village of Nunapitchuk
   */
  NativeVillageOfNunapitchuk: v3_TribalEntityUS_NativeVillageOfNunapitchuk,
  /**
   * Village of Ohogamiut
   */
  VillageOfOhogamiut: v3_TribalEntityUS_VillageOfOhogamiut,
  /**
   * Village of Old Harbor
   */
  VillageOfOldHarbor: v3_TribalEntityUS_VillageOfOldHarbor,
  /**
   * Orutsararmuit Native Village (aka Bethel)
   */
  OrutsararmuitNativeVillageAkaBethel: v3_TribalEntityUS_OrutsararmuitNativeVillageAkaBethel,
  /**
   * Oscarville Traditional Village
   */
  OscarvilleTraditionalVillage: v3_TribalEntityUS_OscarvilleTraditionalVillage,
  /**
   * Native Village of Ouzinkie
   */
  NativeVillageOfOuzinkie: v3_TribalEntityUS_NativeVillageOfOuzinkie,
  /**
   * Native Village of Paimiut
   */
  NativeVillageOfPaimiut: v3_TribalEntityUS_NativeVillageOfPaimiut,
  /**
   * Pauloff Harbor Village
   */
  PauloffHarborVillage: v3_TribalEntityUS_PauloffHarborVillage,
  /**
   * Pedro Bay Village
   */
  PedroBayVillage: v3_TribalEntityUS_PedroBayVillage,
  /**
   * Alabama-Quassarte Tribal Town, Oklahoma
   */
  AlabamaQuassarteTribalTownOklahoma: v3_TribalEntityUS_AlabamaQuassarteTribalTownOklahoma,
  /**
   * Cocopah Tribe of Arizona
   */
  CocopahTribeOfArizona: v3_TribalEntityUS_CocopahTribeOfArizona,
  /**
   * Native Village of Perryville
   */
  NativeVillageOfPerryville: v3_TribalEntityUS_NativeVillageOfPerryville,
  /**
   * Petersburg Indian Association
   */
  PetersburgIndianAssociation: v3_TribalEntityUS_PetersburgIndianAssociation,
  /**
   * Native Village of Pilot Point
   */
  NativeVillageOfPilotPoint: v3_TribalEntityUS_NativeVillageOfPilotPoint,
  /**
   * Pilot Station Traditional Village
   */
  PilotStationTraditionalVillage: v3_TribalEntityUS_PilotStationTraditionalVillage,
  /**
   * Native Village of Pitka's Point
   */
  NativeVillageOfPitkaQuoteSPoint: v3_TribalEntityUS_NativeVillageOfPitkaQuoteSPoint,
  /**
   * Platinum Traditional Village
   */
  PlatinumTraditionalVillage: v3_TribalEntityUS_PlatinumTraditionalVillage,
  /**
   * Native Village of Point Hope
   */
  NativeVillageOfPointHope: v3_TribalEntityUS_NativeVillageOfPointHope,
  /**
   * Native Village of Point Lay
   */
  NativeVillageOfPointLay: v3_TribalEntityUS_NativeVillageOfPointLay,
  /**
   * Native Village of Port Graham
   */
  NativeVillageOfPortGraham: v3_TribalEntityUS_NativeVillageOfPortGraham,
  /**
   * Native Village of Port Heiden
   */
  NativeVillageOfPortHeiden: v3_TribalEntityUS_NativeVillageOfPortHeiden,
  /**
   * Coeur D'Alene Tribe of the Coeur D'Alene Reservati
   */
  CoeurDQuoteAleneTribeOfTheCoeurDQuoteAleneReservati: v3_TribalEntityUS_CoeurDQuoteAleneTribeOfTheCoeurDQuoteAleneReservati,
  /**
   * Native Village of Port Lions
   */
  NativeVillageOfPortLions: v3_TribalEntityUS_NativeVillageOfPortLions,
  /**
   * Portage Creek Village (aka Ohgsenakale)
   */
  PortageCreekVillageAkaOhgsenakale: v3_TribalEntityUS_PortageCreekVillageAkaOhgsenakale,
  /**
   * Pribilof Islands Aleut Communities of St. Paul & S
   */
  PribilofIslandsAleutCommunitiesOfStPaulAndS: v3_TribalEntityUS_PribilofIslandsAleutCommunitiesOfStPaulAndS,
  /**
   * Qagan Tayagungin Tribe of Sand Point Village
   */
  QaganTayagunginTribeOfSandPointVillage: v3_TribalEntityUS_QaganTayagunginTribeOfSandPointVillage,
  /**
   * Qawalangin Tribe of Unalaska
   */
  QawalanginTribeOfUnalaska: v3_TribalEntityUS_QawalanginTribeOfUnalaska,
  /**
   * Rampart Village
   */
  RampartVillage: v3_TribalEntityUS_RampartVillage,
  /**
   * Village of Red Devil
   */
  VillageOfRedDevil: v3_TribalEntityUS_VillageOfRedDevil,
  /**
   * Native Village of Ruby
   */
  NativeVillageOfRuby: v3_TribalEntityUS_NativeVillageOfRuby,
  /**
   * Saint George Island(See Pribilof Islands Aleut Com
   */
  SaintGeorgeIslandSeePribilofIslandsAleutCom: v3_TribalEntityUS_SaintGeorgeIslandSeePribilofIslandsAleutCom,
  /**
   * Native Village of Saint Michael
   */
  NativeVillageOfSaintMichael: v3_TribalEntityUS_NativeVillageOfSaintMichael,
  /**
   * Cold Springs Rancheria of Mono Indians of Californ
   */
  ColdSpringsRancheriaOfMonoIndiansOfCaliforn: v3_TribalEntityUS_ColdSpringsRancheriaOfMonoIndiansOfCaliforn,
  /**
   * Saint Paul Island (See Pribilof Islands Aleut Comm
   */
  SaintPaulIslandSeePribilofIslandsAleutComm: v3_TribalEntityUS_SaintPaulIslandSeePribilofIslandsAleutComm,
  /**
   * Village of Salamatoff
   */
  VillageOfSalamatoff: v3_TribalEntityUS_VillageOfSalamatoff,
  /**
   * Native Village of Savoonga
   */
  NativeVillageOfSavoonga: v3_TribalEntityUS_NativeVillageOfSavoonga,
  /**
   * Organized Village of Saxman
   */
  OrganizedVillageOfSaxman: v3_TribalEntityUS_OrganizedVillageOfSaxman,
  /**
   * Native Village of Scammon Bay
   */
  NativeVillageOfScammonBay: v3_TribalEntityUS_NativeVillageOfScammonBay,
  /**
   * Native Village of Selawik
   */
  NativeVillageOfSelawik: v3_TribalEntityUS_NativeVillageOfSelawik,
  /**
   * Seldovia Village Tribe
   */
  SeldoviaVillageTribe: v3_TribalEntityUS_SeldoviaVillageTribe,
  /**
   * Shageluk Native Village
   */
  ShagelukNativeVillage: v3_TribalEntityUS_ShagelukNativeVillage,
  /**
   * Native Village of Shaktoolik
   */
  NativeVillageOfShaktoolik: v3_TribalEntityUS_NativeVillageOfShaktoolik,
  /**
   * Native Village of Sheldon's Point
   */
  NativeVillageOfSheldonQuoteSPoint: v3_TribalEntityUS_NativeVillageOfSheldonQuoteSPoint,
  /**
   * Colorado River Indian Tribes of the Colorado River
   */
  ColoradoRiverIndianTribesOfTheColoradoRiver: v3_TribalEntityUS_ColoradoRiverIndianTribesOfTheColoradoRiver,
  /**
   * Native Village of Shishmaref
   */
  NativeVillageOfShishmaref: v3_TribalEntityUS_NativeVillageOfShishmaref,
  /**
   * Shoonaq Tribe of Kodiak
   */
  ShoonaqTribeOfKodiak: v3_TribalEntityUS_ShoonaqTribeOfKodiak,
  /**
   * Native Village of Shungnak
   */
  NativeVillageOfShungnak: v3_TribalEntityUS_NativeVillageOfShungnak,
  /**
   * Sitka Tribe of Alaska
   */
  SitkaTribeOfAlaska: v3_TribalEntityUS_SitkaTribeOfAlaska,
  /**
   * Skagway Village
   */
  SkagwayVillage: v3_TribalEntityUS_SkagwayVillage,
  /**
   * Village of Sleetmute
   */
  VillageOfSleetmute: v3_TribalEntityUS_VillageOfSleetmute,
  /**
   * Village of Solomon
   */
  VillageOfSolomon: v3_TribalEntityUS_VillageOfSolomon,
  /**
   * South Naknek Village
   */
  SouthNaknekVillage: v3_TribalEntityUS_SouthNaknekVillage,
  /**
   * Stebbins Community Association
   */
  StebbinsCommunityAssociation: v3_TribalEntityUS_StebbinsCommunityAssociation,
  /**
   * Native Village of Stevens
   */
  NativeVillageOfStevens: v3_TribalEntityUS_NativeVillageOfStevens,
  /**
   * Comanche Indian Tribe, Oklahoma
   */
  ComancheIndianTribeOklahoma: v3_TribalEntityUS_ComancheIndianTribeOklahoma,
  /**
   * Village of Stony River
   */
  VillageOfStonyRiver: v3_TribalEntityUS_VillageOfStonyRiver,
  /**
   * Takotna Village
   */
  TakotnaVillage: v3_TribalEntityUS_TakotnaVillage,
  /**
   * Native Village of Tanacross
   */
  NativeVillageOfTanacross: v3_TribalEntityUS_NativeVillageOfTanacross,
  /**
   * Native Village of Tanana
   */
  NativeVillageOfTanana: v3_TribalEntityUS_NativeVillageOfTanana,
  /**
   * Native Village of Tatitlek
   */
  NativeVillageOfTatitlek: v3_TribalEntityUS_NativeVillageOfTatitlek,
  /**
   * Native Village of Tazlina
   */
  NativeVillageOfTazlina: v3_TribalEntityUS_NativeVillageOfTazlina,
  /**
   * Telida Village
   */
  TelidaVillage: v3_TribalEntityUS_TelidaVillage,
  /**
   * Native Village of Teller
   */
  NativeVillageOfTeller: v3_TribalEntityUS_NativeVillageOfTeller,
  /**
   * Native Village of Tetlin
   */
  NativeVillageOfTetlin: v3_TribalEntityUS_NativeVillageOfTetlin,
  /**
   * Central Council of the Tlingit and Haida Indian Tb
   */
  CentralCouncilOfTheTlingitAndHaidaIndianTb: v3_TribalEntityUS_CentralCouncilOfTheTlingitAndHaidaIndianTb,
  /**
   * Confederated Salish & Kootenai Tribes of the Flath
   */
  ConfederatedSalishAndKootenaiTribesOfTheFlath: v3_TribalEntityUS_ConfederatedSalishAndKootenaiTribesOfTheFlath,
  /**
   * Traditional Village of Togiak
   */
  TraditionalVillageOfTogiak: v3_TribalEntityUS_TraditionalVillageOfTogiak,
  /**
   * Tuluksak Native Community
   */
  TuluksakNativeCommunity: v3_TribalEntityUS_TuluksakNativeCommunity,
  /**
   * Native Village of Tuntutuliak
   */
  NativeVillageOfTuntutuliak: v3_TribalEntityUS_NativeVillageOfTuntutuliak,
  /**
   * Native Village of Tununak
   */
  NativeVillageOfTununak: v3_TribalEntityUS_NativeVillageOfTununak,
  /**
   * Twin Hills Village
   */
  TwinHillsVillage: v3_TribalEntityUS_TwinHillsVillage,
  /**
   * Native Village of Tyonek
   */
  NativeVillageOfTyonek: v3_TribalEntityUS_NativeVillageOfTyonek,
  /**
   * Ugashik Village
   */
  UgashikVillage: v3_TribalEntityUS_UgashikVillage,
  /**
   * Umkumiute Native Village
   */
  UmkumiuteNativeVillage: v3_TribalEntityUS_UmkumiuteNativeVillage,
  /**
   * Native Village of Unalakleet
   */
  NativeVillageOfUnalakleet: v3_TribalEntityUS_NativeVillageOfUnalakleet,
  /**
   * Native Village of Unga
   */
  NativeVillageOfUnga: v3_TribalEntityUS_NativeVillageOfUnga,
  /**
   * Confederated Tribes of the Chehalis Reservation, W
   */
  ConfederatedTribesOfTheChehalisReservationW: v3_TribalEntityUS_ConfederatedTribesOfTheChehalisReservationW,
  /**
   * Village of Venetie (See Native Village of Venetie
   */
  VillageOfVenetieSeeNativeVillageOfVenetie: v3_TribalEntityUS_VillageOfVenetieSeeNativeVillageOfVenetie,
  /**
   * Native Village of Venetie Tribal Government (Arcti
   */
  NativeVillageOfVenetieTribalGovernmentArcti: v3_TribalEntityUS_NativeVillageOfVenetieTribalGovernmentArcti,
  /**
   * Village of Wainwright
   */
  VillageOfWainwright: v3_TribalEntityUS_VillageOfWainwright,
  /**
   * Native Village of Wales
   */
  NativeVillageOfWales: v3_TribalEntityUS_NativeVillageOfWales,
  /**
   * Native Village of White Mountain
   */
  NativeVillageOfWhiteMountain: v3_TribalEntityUS_NativeVillageOfWhiteMountain,
  /**
   * Wrangell Cooperative Association
   */
  WrangellCooperativeAssociation: v3_TribalEntityUS_WrangellCooperativeAssociation,
  /**
   * Yakutat Tlingit Tribe
   */
  YakutatTlingitTribe: v3_TribalEntityUS_YakutatTlingitTribe,
  /**
   * Confederated Tribes of the Colville Reservation, W
   */
  ConfederatedTribesOfTheColvilleReservationW: v3_TribalEntityUS_ConfederatedTribesOfTheColvilleReservationW,
  /**
   * Confederated Tribes of the Coos, Lower Umpqua and
   */
  ConfederatedTribesOfTheCoosLowerUmpquaAnd: v3_TribalEntityUS_ConfederatedTribesOfTheCoosLowerUmpquaAnd,
  /**
   * Confederated Tribes of the Goshute Reservation, Ne
   */
  ConfederatedTribesOfTheGoshuteReservationNe: v3_TribalEntityUS_ConfederatedTribesOfTheGoshuteReservationNe,
  /**
   * Alturas Indian Rancheria, California
   */
  AlturasIndianRancheriaCalifornia: v3_TribalEntityUS_AlturasIndianRancheriaCalifornia,
  /**
   * Confederated Tribes of the Grand Ronde Community o
   */
  ConfederatedTribesOfTheGrandRondeCommunityO: v3_TribalEntityUS_ConfederatedTribesOfTheGrandRondeCommunityO,
  /**
   * Confederated Tribes of the Siletz Reservation, Ore
   */
  ConfederatedTribesOfTheSiletzReservationOre: v3_TribalEntityUS_ConfederatedTribesOfTheSiletzReservationOre,
  /**
   * Confederated Tribes of the Umatilla Reservation, O
   */
  ConfederatedTribesOfTheUmatillaReservationO: v3_TribalEntityUS_ConfederatedTribesOfTheUmatillaReservationO,
  /**
   * Confederated Tribes of the Warm Springs Reservatio
   */
  ConfederatedTribesOfTheWarmSpringsReservatio: v3_TribalEntityUS_ConfederatedTribesOfTheWarmSpringsReservatio,
  /**
   * Confederated Tribes and Bands of the Yakama Indian
   */
  ConfederatedTribesAndBandsOfTheYakamaIndian: v3_TribalEntityUS_ConfederatedTribesAndBandsOfTheYakamaIndian,
  /**
   * Coquille Tribe of Oregon
   */
  CoquilleTribeOfOregon: v3_TribalEntityUS_CoquilleTribeOfOregon,
  /**
   * Cortina Indian Rancheria of Wintun Indians of Cali
   */
  CortinaIndianRancheriaOfWintunIndiansOfCali: v3_TribalEntityUS_CortinaIndianRancheriaOfWintunIndiansOfCali,
  /**
   * Coushatta Tribe of Louisiana
   */
  CoushattaTribeOfLouisiana: v3_TribalEntityUS_CoushattaTribeOfLouisiana,
  /**
   * Cow Creek Band of Umpqua Indians of Oregon
   */
  CowCreekBandOfUmpquaIndiansOfOregon: v3_TribalEntityUS_CowCreekBandOfUmpquaIndiansOfOregon,
  /**
   * Coyote Valley Band of Pomo Indians of California
   */
  CoyoteValleyBandOfPomoIndiansOfCalifornia: v3_TribalEntityUS_CoyoteValleyBandOfPomoIndiansOfCalifornia,
  /**
   * Apache Tribe of Oklahoma
   */
  ApacheTribeOfOklahoma: v3_TribalEntityUS_ApacheTribeOfOklahoma,
  /**
   * Crow Tribe of Montana
   */
  CrowTribeOfMontana: v3_TribalEntityUS_CrowTribeOfMontana,
  /**
   * Crow Creek Sioux Tribe of the Crow Creek Reservati
   */
  CrowCreekSiouxTribeOfTheCrowCreekReservati: v3_TribalEntityUS_CrowCreekSiouxTribeOfTheCrowCreekReservati,
  /**
   * Cuyapaipe Community of Diegueno Mission Indians of
   */
  CuyapaipeCommunityOfDieguenoMissionIndiansOf: v3_TribalEntityUS_CuyapaipeCommunityOfDieguenoMissionIndiansOf,
  /**
   * Death Valley Timbi-Sha Shoshone Band of California
   */
  DeathValleyTimbiShaShoshoneBandOfCalifornia: v3_TribalEntityUS_DeathValleyTimbiShaShoshoneBandOfCalifornia,
  /**
   * Delaware Nation, Oklahoma (formerly Delaware Tribe
   */
  DelawareNationOklahomaFormerlyDelawareTribe: v3_TribalEntityUS_DelawareNationOklahomaFormerlyDelawareTribe,
  /**
   * Delaware Tribe of Indians, Oklahoma
   */
  DelawareTribeOfIndiansOklahoma: v3_TribalEntityUS_DelawareTribeOfIndiansOklahoma,
  /**
   * Dry Creek Rancheria of Pomo Indians of California
   */
  DryCreekRancheriaOfPomoIndiansOfCalifornia: v3_TribalEntityUS_DryCreekRancheriaOfPomoIndiansOfCalifornia,
  /**
   * Duckwater Shoshone Tribe of the Duckwater Reservat
   */
  DuckwaterShoshoneTribeOfTheDuckwaterReservat: v3_TribalEntityUS_DuckwaterShoshoneTribeOfTheDuckwaterReservat,
  /**
   * Eastern Band of Cherokee Indians of North Carolina
   */
  EasternBandOfCherokeeIndiansOfNorthCarolina: v3_TribalEntityUS_EasternBandOfCherokeeIndiansOfNorthCarolina,
  /**
   * Eastern Shawnee Tribe of Oklahoma
   */
  EasternShawneeTribeOfOklahoma: v3_TribalEntityUS_EasternShawneeTribeOfOklahoma,
  /**
   * Arapahoe Tribe of the Wind River Reservation, Wyom
   */
  ArapahoeTribeOfTheWindRiverReservationWyom: v3_TribalEntityUS_ArapahoeTribeOfTheWindRiverReservationWyom,
  /**
   * Elem Indian Colony of Pomo Indians of the Sulphur
   */
  ElemIndianColonyOfPomoIndiansOfTheSulphur: v3_TribalEntityUS_ElemIndianColonyOfPomoIndiansOfTheSulphur,
  /**
   * Elk Valley Rancheria, California
   */
  ElkValleyRancheriaCalifornia: v3_TribalEntityUS_ElkValleyRancheriaCalifornia,
  /**
   * Ely Shoshone Tribe of Nevada
   */
  ElyShoshoneTribeOfNevada: v3_TribalEntityUS_ElyShoshoneTribeOfNevada,
  /**
   * Enterprise Rancheria of Maidu Indians of Californi
   */
  EnterpriseRancheriaOfMaiduIndiansOfCaliforni: v3_TribalEntityUS_EnterpriseRancheriaOfMaiduIndiansOfCaliforni,
  /**
   * Flandreau Santee Sioux Tribe of South Dakota
   */
  FlandreauSanteeSiouxTribeOfSouthDakota: v3_TribalEntityUS_FlandreauSanteeSiouxTribeOfSouthDakota,
  /**
   * Forest County Potawatomi Community of Wisconsin Po
   */
  ForestCountyPotawatomiCommunityOfWisconsinPo: v3_TribalEntityUS_ForestCountyPotawatomiCommunityOfWisconsinPo,
  /**
   * Fort Belknap Indian Community of the Fort Belknap
   */
  FortBelknapIndianCommunityOfTheFortBelknap: v3_TribalEntityUS_FortBelknapIndianCommunityOfTheFortBelknap,
  /**
   * Fort Bidwell Indian Community of the Fort Bidwell
   */
  FortBidwellIndianCommunityOfTheFortBidwell: v3_TribalEntityUS_FortBidwellIndianCommunityOfTheFortBidwell,
  /**
   * Fort Independence Indian Community of Paiute India
   */
  FortIndependenceIndianCommunityOfPaiuteIndia: v3_TribalEntityUS_FortIndependenceIndianCommunityOfPaiuteIndia,
  /**
   * Fort McDermitt Paiute and Shoshone Tribes of the F
   */
  FortMcDermittPaiuteAndShoshoneTribesOfTheF: v3_TribalEntityUS_FortMcDermittPaiuteAndShoshoneTribesOfTheF,
  /**
   * Aroostook Band of Micmac Indians of Maine
   */
  AroostookBandOfMicmacIndiansOfMaine: v3_TribalEntityUS_AroostookBandOfMicmacIndiansOfMaine,
  /**
   * Fort McDowell Yavapai Nation, Arizona (formerly th
   */
  FortMcDowellYavapaiNationArizonaFormerlyTh: v3_TribalEntityUS_FortMcDowellYavapaiNationArizonaFormerlyTh,
  /**
   * Fort Mojave Indian Tribe of Arizona, California
   */
  FortMojaveIndianTribeOfArizonaCalifornia: v3_TribalEntityUS_FortMojaveIndianTribeOfArizonaCalifornia,
  /**
   * Fort Sill Apache Tribe of Oklahoma
   */
  FortSillApacheTribeOfOklahoma: v3_TribalEntityUS_FortSillApacheTribeOfOklahoma,
  /**
   * Gila River Indian Community of the Gila River Indi
   */
  GilaRiverIndianCommunityOfTheGilaRiverIndi: v3_TribalEntityUS_GilaRiverIndianCommunityOfTheGilaRiverIndi,
  /**
   * Grand Traverse Band of Ottawa & Chippewa Indians o
   */
  GrandTraverseBandOfOttawaAndChippewaIndiansO: v3_TribalEntityUS_GrandTraverseBandOfOttawaAndChippewaIndiansO,
  /**
   * Graton Rancheria, California
   */
  GratonRancheriaCalifornia: v3_TribalEntityUS_GratonRancheriaCalifornia,
  /**
   * Greenville Rancheria of Maidu Indians of Californi
   */
  GreenvilleRancheriaOfMaiduIndiansOfCaliforni: v3_TribalEntityUS_GreenvilleRancheriaOfMaiduIndiansOfCaliforni,
  /**
   * Grindstone Indian Rancheria of Wintun-Wailaki Indi
   */
  GrindstoneIndianRancheriaOfWintunWailakiIndi: v3_TribalEntityUS_GrindstoneIndianRancheriaOfWintunWailakiIndi,
  /**
   * Guidiville Rancheria of California
   */
  GuidivilleRancheriaOfCalifornia: v3_TribalEntityUS_GuidivilleRancheriaOfCalifornia,
  /**
   * Hannahville Indian Community of Wisconsin Potawato
   */
  HannahvilleIndianCommunityOfWisconsinPotawato: v3_TribalEntityUS_HannahvilleIndianCommunityOfWisconsinPotawato,
};
const v3_triggerEventID_ResultStatus: Coding = {
  code: 'POLB_TE004000UV',
  display: 'Result Status',
  system: 'http://terminology.hl7.org/CodeSystem/v3-triggerEventID',
};
const v3_triggerEventID_ResultConfirm: Coding = {
  code: 'POLB_TE004001UV',
  display: 'Result Confirm',
  system: 'http://terminology.hl7.org/CodeSystem/v3-triggerEventID',
};
const v3_triggerEventID_ResultReject: Coding = {
  code: 'POLB_TE004002UV',
  display: 'Result Reject',
  system: 'http://terminology.hl7.org/CodeSystem/v3-triggerEventID',
};
const v3_triggerEventID_ResultTracking: Coding = {
  code: 'POLB_TE004007UV',
  display: 'Result Tracking',
  system: 'http://terminology.hl7.org/CodeSystem/v3-triggerEventID',
};
const v3_triggerEventID_ResultInProgress: Coding = {
  code: 'POLB_TE004100UV',
  display: 'Result in Progress',
  system: 'http://terminology.hl7.org/CodeSystem/v3-triggerEventID',
};
const v3_triggerEventID_ResultActivate: Coding = {
  code: 'POLB_TE004102UV',
  display: 'Result Activate',
  system: 'http://terminology.hl7.org/CodeSystem/v3-triggerEventID',
};
const v3_triggerEventID_ResultCompleteWithFulfillment: Coding = {
  code: 'POLB_TE004200UV',
  display: 'Result Complete with Fulfillment',
  system: 'http://terminology.hl7.org/CodeSystem/v3-triggerEventID',
};
const v3_triggerEventID_ResultCorrected: Coding = {
  code: 'POLB_TE004201UV',
  display: 'Result Corrected',
  system: 'http://terminology.hl7.org/CodeSystem/v3-triggerEventID',
};
const v3_triggerEventID_ResultComplete: Coding = {
  code: 'POLB_TE004202UV',
  display: 'Result Complete',
  system: 'http://terminology.hl7.org/CodeSystem/v3-triggerEventID',
};
const v3_triggerEventID_ResultAbort: Coding = {
  code: 'POLB_TE004301UV',
  display: 'Result Abort',
  system: 'http://terminology.hl7.org/CodeSystem/v3-triggerEventID',
};
const v3_triggerEventID_ResultNullify: Coding = {
  code: 'POLB_TE004500UV',
  display: 'Result Nullify',
  system: 'http://terminology.hl7.org/CodeSystem/v3-triggerEventID',
};
/**
 *   Description:
 * This code system contains all HL7 artifacts of type TE (Trigger Event) that are created by HL7 or its affiliates or their designates using the realm namespacing rules approved by HL7.  Local implementations who create trigger events outside of these namespacing rules, (e.g. using the ZZ realm code) must register their own code system.  The specific list of legal codes can be found by consulting the HL7 publications (editions, ballots, implementation guides, etc.) published by HL7 Inc. and by the various HL7 affiliates and their designates.  Codes shall be expressed in upper case, with separator as shown in HL7 publications with no version id.  E.g. PORX_TE123456UV.
 */
export const V3TriggerEventID = {
  /**
   * Description:
   */
  ResultStatus: v3_triggerEventID_ResultStatus,
  /**
   * Description:
   */
  ResultConfirm: v3_triggerEventID_ResultConfirm,
  /**
   * Description:
   */
  ResultReject: v3_triggerEventID_ResultReject,
  /**
   * Description:
   */
  ResultTracking: v3_triggerEventID_ResultTracking,
  /**
   * Description:
   */
  ResultInProgress: v3_triggerEventID_ResultInProgress,
  /**
   * Description:
   */
  ResultActivate: v3_triggerEventID_ResultActivate,
  /**
   * Description:
   */
  ResultCompleteWithFulfillment: v3_triggerEventID_ResultCompleteWithFulfillment,
  /**
   * Description:
   */
  ResultCorrected: v3_triggerEventID_ResultCorrected,
  /**
   * Description:
   */
  ResultComplete: v3_triggerEventID_ResultComplete,
  /**
   * Description:
   */
  ResultAbort: v3_triggerEventID_ResultAbort,
  /**
   * Description:
   */
  ResultNullify: v3_triggerEventID_ResultNullify,
};
const v3_VaccineManufacturer_AB: Coding = {
  code: 'AB',
  display: 'Abbott Laboratories (includes Ross Products Division)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_AD: Coding = {
  code: 'AD',
  display: 'Adams Laboratories',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_ALP: Coding = {
  code: 'ALP',
  display: 'Alpha Therapeutic Corporation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_AR: Coding = {
  code: 'AR',
  display: 'Armour [Inactive - use CEN]',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_AVI: Coding = {
  code: 'AVI',
  display: 'Aviron',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_BA: Coding = {
  code: 'BA',
  display: 'Baxter Healthcare Corporation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_BAY: Coding = {
  code: 'BAY',
  display: 'Bayer Corporation (includes Miles, Inc. and Cutter Laboratories)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_BP: Coding = {
  code: 'BP',
  display: 'Berna Products [Inactive - use BPC]',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_BPC: Coding = {
  code: 'BPC',
  display: 'Berna Products Corporation (includes Swiss Serum and Vaccine Institute Berne)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_CEN: Coding = {
  code: 'CEN',
  display: 'Centeon L.L.C. (includes Armour Pharmaceutical Company)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_CHI: Coding = {
  code: 'CHI',
  display: 'Chiron Corporation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_CON: Coding = {
  code: 'CON',
  display: 'Connaught [Inactive - use PMC]',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_EVN: Coding = {
  code: 'EVN',
  display: 'Evans Medical Limited (an affiliate of Medeva Pharmaceuticals, Inc.)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_GRE: Coding = {
  code: 'GRE',
  display: 'Greer Laboratories, Inc.',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_IAG: Coding = {
  code: 'IAG',
  display: 'Immuno International AG',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_IM: Coding = {
  code: 'IM',
  display: 'Merieux [Inactive - use PMC]',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_IUS: Coding = {
  code: 'IUS',
  display: 'Immuno-U.S., Inc.',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_JPN: Coding = {
  code: 'JPN',
  display: 'The Research Foundation for Microbial Diseases of Osaka University (BIKEN)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_KGC: Coding = {
  code: 'KGC',
  display: 'Korea Green Cross Corporation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_LED: Coding = {
  code: 'LED',
  display: 'Lederle [Inactive - use WAL]',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_MA: Coding = {
  code: 'MA',
  display: 'Massachusetts Public Health Biologic Laboratories',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_MED: Coding = {
  code: 'MED',
  display: 'MedImmune, Inc.',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_MIL: Coding = {
  code: 'MIL',
  display: 'Miles [Inactive - use BAY]',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_MIP: Coding = {
  code: 'MIP',
  display: 'Bioport Corporation (formerly Michigan Biologic Products Institute)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_MSD: Coding = {
  code: 'MSD',
  display: 'Merck and Co., Inc.',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_NAB: Coding = {
  code: 'NAB',
  display: 'NABI (formerly North American Biologicals, Inc.)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_NAV: Coding = {
  code: 'NAV',
  display: 'North American Vaccine, Inc.',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_NOV: Coding = {
  code: 'NOV',
  display: 'Novartis Pharmaceutical Corporation (includes Ciba-Geigy Limited and Sandoz Limited)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_NYB: Coding = {
  code: 'NYB',
  display: 'New York Blood Center',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_ORT: Coding = {
  code: 'ORT',
  display: 'Ortho Diagnostic Systems, Inc.',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_OTC: Coding = {
  code: 'OTC',
  display: 'Organon Teknika Corporation',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_PD: Coding = {
  code: 'PD',
  display: 'Parkedale Pharmaceuticals (formerly Parke-Davis)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_PMC: Coding = {
  code: 'PMC',
  display: 'Aventis Pasteur Inc. (formerly Pasteur Merieux Connaught; includes Connaught Laboratories and Pasteur Merieux)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_PRX: Coding = {
  code: 'PRX',
  display: 'Praxis Biologics [Inactive - use WAL]',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_SCL: Coding = {
  code: 'SCL',
  display: 'Sclavo, Inc.',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_SI: Coding = {
  code: 'SI',
  display: 'Swiss Serum and Vaccine Inst. [Inactive - use BPC]',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_SKB: Coding = {
  code: 'SKB',
  display: 'SmithKline Beecham',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_USA: Coding = {
  code: 'USA',
  display: 'United States Army Medical Research and Materiel Command',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_WA: Coding = {
  code: 'WA',
  display: 'Wyeth-Ayerst [Inactive - use WAL]',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
const v3_VaccineManufacturer_WAL: Coding = {
  code: 'WAL',
  display: 'Wyeth-Ayerst (includes Wyeth-Lederle Vaccines and Pediatrics, Wyeth Laboratories, Lederle Laboratories, and Praxis Biologics)',
  system: 'http://terminology.hl7.org/CodeSystem/v3-VaccineManufacturer',
};
/**
 *  The manufacturer of a vaccine.
 */
export const V3VaccineManufacturer = {
  /**
   * Abbott Laboratories (includes Ross Products Division)
   */
  AB: v3_VaccineManufacturer_AB,
  /**
   * Adams Laboratories
   */
  AD: v3_VaccineManufacturer_AD,
  /**
   * Alpha Therapeutic Corporation
   */
  ALP: v3_VaccineManufacturer_ALP,
  /**
   * Armour [Inactive-use CEN]
   */
  AR: v3_VaccineManufacturer_AR,
  /**
   * Aviron
   */
  AVI: v3_VaccineManufacturer_AVI,
  /**
   * Baxter Healthcare Corporation
   */
  BA: v3_VaccineManufacturer_BA,
  /**
   * Bayer Corporation (includes Miles, Inc. and Cutter Laboratories)
   */
  BAY: v3_VaccineManufacturer_BAY,
  /**
   * Berna Products [Inactive-use BPC]
   */
  BP: v3_VaccineManufacturer_BP,
  /**
   * Berna Products Corporation (includes Swiss Serum and Vaccine Institute Berne)
   */
  BPC: v3_VaccineManufacturer_BPC,
  /**
   * Centeon L.L.C. (includes Armour Pharmaceutical Company)
   */
  CEN: v3_VaccineManufacturer_CEN,
  /**
   * Chiron Corporation
   */
  CHI: v3_VaccineManufacturer_CHI,
  /**
   * Connaught [Inactive-use PMC]
   */
  CON: v3_VaccineManufacturer_CON,
  /**
   * Evans Medical Limited (an affiliate of Medeva Pharmaceuticals, Inc.)
   */
  EVN: v3_VaccineManufacturer_EVN,
  /**
   * Greer Laboratories, Inc.
   */
  GRE: v3_VaccineManufacturer_GRE,
  /**
   * Immuno International AG
   */
  IAG: v3_VaccineManufacturer_IAG,
  /**
   * Merieux [Inactive-use PMC]
   */
  IM: v3_VaccineManufacturer_IM,
  /**
   * Immuno-U.S., Inc.
   */
  IUS: v3_VaccineManufacturer_IUS,
  /**
   * The Research Foundation for Microbial Diseases of Osaka University (BIKEN)
   */
  JPN: v3_VaccineManufacturer_JPN,
  /**
   * Korea Green Cross Corporation
   */
  KGC: v3_VaccineManufacturer_KGC,
  /**
   * Lederle [Inactive-use WAL]
   */
  LED: v3_VaccineManufacturer_LED,
  /**
   * Massachusetts Public Health Biologic Laboratories
   */
  MA: v3_VaccineManufacturer_MA,
  /**
   * MedImmune, Inc.
   */
  MED: v3_VaccineManufacturer_MED,
  /**
   * Miles [Inactive-use BAY]
   */
  MIL: v3_VaccineManufacturer_MIL,
  /**
   * Bioport Corporation (formerly Michigan Biologic Products Institute)
   */
  MIP: v3_VaccineManufacturer_MIP,
  /**
   * Merck & Co., Inc.
   */
  MSD: v3_VaccineManufacturer_MSD,
  /**
   * NABI (formerly North American Biologicals, Inc.)
   */
  NAB: v3_VaccineManufacturer_NAB,
  /**
   * North American Vaccine, Inc.
   */
  NAV: v3_VaccineManufacturer_NAV,
  /**
   * Novartis Pharmaceutical Corporation (includes Ciba-Geigy Limited and Sandoz Limited)
   */
  NOV: v3_VaccineManufacturer_NOV,
  /**
   * New York Blood Center
   */
  NYB: v3_VaccineManufacturer_NYB,
  /**
   * Ortho Diagnostic Systems, Inc.
   */
  ORT: v3_VaccineManufacturer_ORT,
  /**
   * Organon Teknika Corporation
   */
  OTC: v3_VaccineManufacturer_OTC,
  /**
   * Parkedale Pharmaceuticals (formerly Parke-Davis)
   */
  PD: v3_VaccineManufacturer_PD,
  /**
   * Aventis Pasteur Inc. (formerly Pasteur Merieux Connaught; includes Connaught Laboratories and Pasteur Merieux)
   */
  PMC: v3_VaccineManufacturer_PMC,
  /**
   * Praxis Biologics [Inactive-use WAL]
   */
  PRX: v3_VaccineManufacturer_PRX,
  /**
   * Sclavo, Inc.
   */
  SCL: v3_VaccineManufacturer_SCL,
  /**
   * Swiss Serum and Vaccine Inst. [Inactive-use BPC]
   */
  SI: v3_VaccineManufacturer_SI,
  /**
   * SmithKline Beecham
   */
  SKB: v3_VaccineManufacturer_SKB,
  /**
   * United States Army Medical Research and Materiel Command
   */
  USA: v3_VaccineManufacturer_USA,
  /**
   * Wyeth-Ayerst [Inactive-use WAL]
   */
  WA: v3_VaccineManufacturer_WA,
  /**
   * Wyeth-Ayerst (includes Wyeth-Lederle Vaccines and Pediatrics, Wyeth Laboratories, Lederle Laboratories, and Praxis Biologics)
   */
  WAL: v3_VaccineManufacturer_WAL,
};
/**
 * No Description Provided
 */
export const V3VerificationMethod = {
  /**
   * VerificationMethod
   */
  VerificationMethod: v3_ObservationMethod_VerificationMethod,
  /**
   * Verification by means of document.
   *
   *
   *                            Example: Fax, letter, attachment to e-mail.
   */
  DocumentVerification: v3_ObservationMethod_DocumentVerification,
  /**
   * verification by means of  a response to an electronic query
   *
   *
   *                            Example: query message to a Covered Party registry application or Coverage Administrator.
   */
  RegistryVerification: v3_ObservationMethod_RegistryVerification,
  /**
   * Verification by means of electronic token.
   *
   *
   *                            Example: smartcard, magnetic swipe card, RFID device.
   */
  ElectronicTokenVerification: v3_ObservationMethod_ElectronicTokenVerification,
  /**
   * Verification by means of voice.
   *
   *
   *                            Example: By speaking with or calling the Coverage Administrator or Covered Party
   */
  VoiceBasedVerification: v3_ObservationMethod_VoiceBasedVerification,
};
const v3_WorkClassificationODH_PaidWorkArmedForces: Coding = {
  code: 'PWAF',
  display: 'Paid work, Armed Forces',
  system: 'http://terminology.hl7.org/CodeSystem/v3-WorkClassificationODH',
};
const v3_WorkClassificationODH_PaidWorkNationalGovernmentNotArmedForces: Coding = {
  code: 'PWFG',
  display: 'Paid work, national government, not armed forces',
  system: 'http://terminology.hl7.org/CodeSystem/v3-WorkClassificationODH',
};
const v3_WorkClassificationODH_PaidWorkLocalGovernmentNotArmedForces: Coding = {
  code: 'PWLG',
  display: 'Paid work, local government, not armed forces',
  system: 'http://terminology.hl7.org/CodeSystem/v3-WorkClassificationODH',
};
const v3_WorkClassificationODH_PaidNonGovernmentalWorkNotSelfEmployed: Coding = {
  code: 'PWNSE',
  display: 'Paid non-governmental work, not self-employed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-WorkClassificationODH',
};
const v3_WorkClassificationODH_PaidWorkSelfEmployed: Coding = {
  code: 'PWSE',
  display: 'Paid work, self-employed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-WorkClassificationODH',
};
const v3_WorkClassificationODH_PaidWorkStateGovernmentNotArmedForces: Coding = {
  code: 'PWSG',
  display: 'Paid work, state government, not armed forces',
  system: 'http://terminology.hl7.org/CodeSystem/v3-WorkClassificationODH',
};
const v3_WorkClassificationODH_UnpaidNonGovernmentalWorkNotSelfEmployed: Coding = {
  code: 'UWNSE',
  display: 'Unpaid non-governmental work, not self-employed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-WorkClassificationODH',
};
const v3_WorkClassificationODH_UnpaidWorkSelfEmployed: Coding = {
  code: 'UWSE',
  display: 'Unpaid work, self-employed',
  system: 'http://terminology.hl7.org/CodeSystem/v3-WorkClassificationODH',
};
const v3_WorkClassificationODH_VoluntaryWork: Coding = {
  code: 'VW',
  display: 'Voluntary work',
  system: 'http://terminology.hl7.org/CodeSystem/v3-WorkClassificationODH',
};
/**
 *  Code system of concepts representing a person's job type as defined by compensation and sector (e.g. paid vs. unpaid, self-employed vs. not self-employed, government vs. private, etc.).
 */
export const V3WorkClassificationODH = {
  /**
   * A situation in which an individual serves in a government-sponsored military force.
   */
  PaidWorkArmedForces: v3_WorkClassificationODH_PaidWorkArmedForces,
  /**
   * A situation in which an individual works for a national government organization, not including armed forces, and receives a paid salary or wage.
   */
  PaidWorkNationalGovernmentNotArmedForces: v3_WorkClassificationODH_PaidWorkNationalGovernmentNotArmedForces,
  /**
   * A situation in which an individual works for a government organization with jurisdiction below the level of state/provincial/territorial/tribal government (e.g., city, town, township), not armed forces, and receives a paid salary or wage.
   */
  PaidWorkLocalGovernmentNotArmedForces: v3_WorkClassificationODH_PaidWorkLocalGovernmentNotArmedForces,
  /**
   * A situation in which an individual works for a business (not government) that they do not own and receives a paid salary or wage.
   */
  PaidNonGovernmentalWorkNotSelfEmployed: v3_WorkClassificationODH_PaidNonGovernmentalWorkNotSelfEmployed,
  /**
   * A situation in which an individual earns a salary or wage working for himself or herself instead of working for an employer.
   */
  PaidWorkSelfEmployed: v3_WorkClassificationODH_PaidWorkSelfEmployed,
  /**
   * A situation in which an individual works for a government organization with jurisdiction immediately below the level of national government (between national government and local government), not armed forces and receives a paid salary or wage.  Often called a state, provincial, territorial, or tribal government.
   */
  PaidWorkStateGovernmentNotArmedForces: v3_WorkClassificationODH_PaidWorkStateGovernmentNotArmedForces,
  /**
   * A situation in which an individual works for a business (not government) that they do not own without receiving a paid salary or wage.
   */
  UnpaidNonGovernmentalWorkNotSelfEmployed: v3_WorkClassificationODH_UnpaidNonGovernmentalWorkNotSelfEmployed,
  /**
   * A situation in which an individual works for himself or herself without receiving a paid salary or wage.
   */
  UnpaidWorkSelfEmployed: v3_WorkClassificationODH_UnpaidWorkSelfEmployed,
  /**
   * A situation in which an individual chooses to do something, especially for other people or for an organization, willingly and without being forced or compensated to do it.  This can include formal activity undertaken through public, private and voluntary organizations as well as informal community participation.
   */
  VoluntaryWork: v3_WorkClassificationODH_VoluntaryWork,
};
/**
 *  Describes an individual's typical arrangement of working hours for an occupation.
 */
export const V3WorkScheduleODH = {
  /**
   * A person who is scheduled for work during daytime hours (for example between 6am and 6pm) on a regular basis.
   */
  DaytimeShift: v3_ObservationValue_DaytimeShift,
  /**
   * Consistent Early morning schedule of 13 hours or less per shift (between 2 am and 2 pm)
   */
  EarlyMorningShift: v3_ObservationValue_EarlyMorningShift,
  /**
   * A person who is scheduled for work during evening hours (for example between 2pm and midnight) on a regular basis.
   */
  EveningShift: v3_ObservationValue_EveningShift,
  /**
   * Scheduled for work during nighttime hours (for example between 9pm and 8am) on a regular basis.
   */
  NightShift: v3_ObservationValue_NightShift,
  /**
   * Scheduled for work times that change periodically between days, and/or evenings, and includes some night shifts.
   */
  RotatingShiftWithNights: v3_ObservationValue_RotatingShiftWithNights,
  /**
   * Scheduled for work days/times that change periodically between days, but does not include night or evening work.
   */
  RotatingShiftWithoutNights: v3_ObservationValue_RotatingShiftWithoutNights,
  /**
   * Shift consisting of two distinct work periods each day that are separated by a break of a few hours (for example 2 to 4 hours)
   */
  SplitShift: v3_ObservationValue_SplitShift,
  /**
   * Shifts of 17 or more hours.
   */
  VeryLongShift: v3_ObservationValue_VeryLongShift,
  /**
   * Irregular, unpredictable hours scheduled on a short notice (for example, less than 2 day notice): inconsistent schedule, on-call, as needed, as available.
   */
  VariableShift: v3_ObservationValue_VariableShift,
};
/**
 *   Description:
 * Used to enumerate the typical confidentiality constraints placed upon a clinical document.  Usage Note:
 * x_BasicConfidentialityKind is a subset of Confidentiality codes that are used as metadata indicating the receiver responsibility to comply with normally applicable jurisdictional privacy law or disclosure authorization; that the receiver may not disclose this information except as directed by the information custodian, who may be the information subject; or that the receiver may not disclose this information except as directed by the information custodian, who may be the information subject.
 */
export const V3XBasicConfidentialityKind = {
  /**
   * Definition: Privacy metadata indicating that the information is typical, non-stigmatizing health information, which presents typical risk of harm if disclosed without authorization.
   *
   *
   *                            Examples: In the US, this includes what HIPAA identifies as the minimum necessary protected health information (PHI) given a covered purpose of use (treatment, payment, or operations).  Includes typical, non-stigmatizing health information disclosed in an application for health, workers compensation, disability, or life insurance.
   *
   *
   *                            Map: Partial Map to ISO 13606-4 Sensitivity Level (3) Clinical Care:   Default for normal clinical care access (i.e. most clinical staff directly caring for the patient should be able to access nearly all of the EHR).   Maps to normal confidentiality for treatment information but not to ancillary care, payment and operations.
   *
   *
   *                            Usage Note: This metadata indicates that the receiver may be obligated to comply with applicable jurisdictional privacy law or disclosure authorization.
   */
  Normal: v3_Confidentiality_Normal,
  /**
   * Privacy metadata indicating highly sensitive, potentially stigmatizing information, which presents a high risk to the information subject if disclosed without authorization. May be pre-empted by jurisdictional law, e.g., for public health reporting or emergency treatment.
   *
   *
   *                            Examples: Includes information that is additionally protected such as sensitive conditions mental health, HIV, substance abuse, domestic violence, child abuse, genetic disease, and reproductive health; or sensitive demographic information such as a patient's standing as an employee or a celebrity. May be used to indicate proprietary or classified information that is not related to an individual, e.g., secret ingredients in a therapeutic substance; or the name of a manufacturer.
   *
   *
   *                            Map: Partial Map to ISO 13606-4 Sensitivity Level (3) Clinical Care: Default for normal clinical care access (i.e. most clinical staff directly caring for the patient should be able to access nearly all of the EHR). Maps to normal confidentiality for treatment information but not to ancillary care, payment and operations..
   *
   *
   *                            Usage Note: This metadata indicates that the receiver may be obligated to comply with applicable, prevailing (default) jurisdictional privacy law or disclosure authorization..
   */
  Restricted: v3_Confidentiality_Restricted,
  /**
   * . Privacy metadata indicating that the information is extremely sensitive and likely stigmatizing health information that presents a very high risk if disclosed without authorization.  This information must be kept in the highest confidence.
   *
   *
   *                            Examples:  Includes information about a victim of abuse, patient requested information sensitivity, and taboo subjects relating to health status that must be discussed with the patient by an attending provider before sharing with the patient.  May also include information held under â€œlegal lockâ€? or attorney-client privilege
   *
   *
   *                            Map:  This metadata indicates that the receiver may not disclose this information except as directed by the information custodian, who may be the information subject.
   *
   *
   *                            Usage Note:  This metadata indicates that the receiver may not disclose this information except as directed by the information custodian, who may be the information subject.
   */
  VeryRestricted: v3_Confidentiality_VeryRestricted,
};
